import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { scoEcoBands, scoEcoSlices, scoEcoBandLabel, SCO_ECO_BAND_CHANGE_YEAR,
         areaCoveragePct, areaCoverageFractionOfPct, endorsementCoveragePct, areaCoverageLabel,
         AREA_COVERAGE_PCT_MIN, AREA_COVERAGE_PCT_MAX, AREA_COVERAGE_PCT_DEFAULT } from "./scoEcoBands";
import { YEAR_IDENTITY, asCropYear, priorCropYear, seasonCropYear, SEASONS, availableCropYears, resolveSelectedYear, electionYearContext, readStoredYear, writeStoredYear, assertYearIdentity } from "./cropYear";
import { wheatPpFactorPrior, WHEAT_PP_PRIOR_YEAR } from "./wheatPpPrior";
import { PP_FACTORS, ppBaseFactor, resolvePpFactor, ppFactorLabel, PP_BUYUP_MIN, PP_BUYUP_MAX, ppStateFips } from "./ppFactor";
import { quoteWheatRP2027, quoteAreaRP2027, wheatProjectedPrice2027, computeApprovedYield2027, wheatYieldRefs2027, wheatPractices2027, wheatPpFactor2027, wheatOptions2027, WHEAT_PRACTICE_NAMES, isCertifiedOrganic, isOrganicPractice, WHEAT_2027, WHEAT_STATES, WHEAT_COUNTIES_BY_STATE, MPCI_WHEAT_YEAR, APH_MAX_YEARS, WHEAT_2027_PROJECTED_APPROVAL, WHEAT_2027_ORGANIC_PROJECTED, WHEAT_2027_VOLATILITY_RELEASE, rmaPriceCents } from "./mpciWheat2027";
// The wheat area-fit rules. `areaFit` answers "when THIS farm has a bad year,
// does the county trigger also have one" off RMA's own county history, which is
// what SCO and ECO settle on — replacing a Pearson against NASS STATE yield.
// `recommendWheatCoverage` prices the whole 75/80/85 ladder as complete
// structures so the 5-point bands can be costed net of the SCO premium the
// grower stops paying. See src/wheat/areaFit.js for the measurement.
import { areaFit, FIT as WHEAT_FIT, FIT_LABEL as WHEAT_FIT_LABEL, areaFitSentence,
         SCO_TRIGGER_PCT as WHEAT_SCO_TOP } from "./wheat/areaFit";
import { recommendWheatCoverage, ECON_LABEL as WHEAT_ECON_LABEL,
         CLASSIFICATION as WHEAT_CLASS, CLASSIFICATION_LABEL as WHEAT_CLASS_LABEL,
         classificationLabel as wheatClassLabel,
         postalStateOf as wheatPostalState } from "./wheat/coverageRecommend";
import { NEED_LABEL as WHEAT_NEED_LABEL, individualNeedSentence } from "./wheat/individualNeed";
// The coverage-level optimizer. A toggle, default OFF, underlying RP only — see
// src/wheat/levelReplay.js for the rules and why it is a ledger and not a verdict.
import LevelOptimizerPanel from "./wheat/LevelOptimizerPanel";
import { crossCheckBook, describeCheck, VERDICT as CC_VERDICT, compareEstimateToPaid, describeEstimate, ESTIMATE as CC_ESTIMATE, ESTIMATE_TOLERANCE as CC_EST_TOL } from "./claims/claimCrossCheck";
import { buildExpectedBook, currentLines as cxCurrentLines, LANE as CX_LANE, BASIS as CX_BASIS } from "./claims/claimExpected";
// ⚠ ONE WORD PER LANE, USED BY BOTH THE PARTITION STRIP AND EVERY FARM ROW. The two
// used to phrase the same lane differently — the strip said "agree to the dollar" where a
// row said "match", and a row said "not compared" for what the strip splits into pending
// and not-estimated. A reader cannot add up a table whose parts are named twice.
const CX_LANE_WORD = Object.freeze({ agree: "agree", look: "to look at", pending: "pending",
  unpaid: "unpaid", notest: "not estimated", other: "unclassified" });
import { reconcileCountyProduction, jdCountySuggestion } from "./claims/countyProduction";
import { isHowlettSource } from "./howlettBids";
// The claim arithmetic itself. `computeHarvestClaim` was defined in this file and is
// now shared, so the estimate that gets frozen and the roster that displays it are one
// rule. Prevent plant has ONE engine, `adjudicatePreventPlant` (imported below); the
// unused second one, `estimatePreventPlant`, was deleted 2026-10-19.
import { computeHarvestClaim, harvestSourcesFromInputs, estimateReadiness, describeReadiness, READY as CE_READY } from "./claims/claimEstimate";
// The prevent-plant DETERMINATION — eligibility, the roll across the policy, and what is
// left undetermined. `ppCells` is the assembly, shared with tools/pp-adjuster-report.js so
// the screen and the book report cannot answer about different determinations.
import { adjudicateFarm, ADJUDICATION_VERSION } from "./claims/ppAdjuster";
// A 578 that arrived before the AIP keyed its acres can feed THAT engine a provisional
// prevent-plant input, labelled as such, where a PP claim is open (admin only).
import { applyProvisional578Pp, openPpClaimKeys, PROVISIONAL_LABEL } from "./claims/provisional578";
// The one read of an arrived 578 (sql/104) and the one rule for which 578s to read together.
import * as FSA578LINES from "./fsa578Lines";
// The ONE per-acre prevent-plant rate. The farmer card, the Claims Manager roster and the
// adjuster all price an acre through it — see src/claims/ppRate.js.
import { ppDollarsPerAcre } from "./claims/ppRate";
import { claimUnit, unitIsAssumed, claimBasis, coverageBasis, claimCropsInBook, claimCropLabel, basisNote, claimCampaigns, PP_CAMPAIGN_KEY } from "./claims/claimCrops";
import { buildPriceIndex, buildPpCells, buildModel } from "./claims/ppCells";
import { buildProductionEvidence, appraisalsByCounty, countyOverUnder, countyGuaranteeRead } from "./claims/productionEvidence";
import UnitGuaranteePanel from "./claims/UnitGuaranteePanel";
import { buildProductionReportLines } from "./claims/productionReportLine";
import { buildFieldCoverage, acreAccounting, dropHmcRows, MATCH as FC_MATCH } from "./claims/fieldCoverage";
import { claimAccountingView, claimTodos, grainCombineOf, COMBINE_COUNTED_CROPS } from "./claims/claimAccounting";
import { buildUnitYield, STATUS as UY_STATUS, UNIT_YIELD_NEEDS } from "./claims/unitYield";
import { jdEvidenceOnly, filterJdEvidenceOps, isJdEvidenceRow, jdSeasonOf, JD_EVIDENCE_STATUS_COLUMNS } from "./claims/jdEvidence";
import { buildProductionSources, acreBasis } from "./claims/productionSources";
import { partitionTracked, cardHarvestComplete, paceExclusionKey, partitionPaceExcluded, paceExclusionSummary, noHarvestDataHint } from "./fieldProgress/trackedSource";
import { blockingFieldLabel as fpBlockingFieldLabel, harvestDenominator as fpHarvestDen } from "./fieldProgress/harvestComplete";
// The renewal-season pace and the visit route. Both render only; every rule they draw lives
// in src/renewals/renewalSeason.js and src/renewals/visitRoute.js, which are pure and tested
// offline by tools/check-renewal-visits.js.
import { VisitPace, VisitRoutePlanner } from "./renewals/VisitPlanner";
import { renewalPace as renewalPaceRule } from "./renewals/renewalSeason";
import { groupFieldPoints, resolveFarmLocations } from "./renewals/farmLocation";
import { findDoubleInsured, OVERLAP_MIN_AC as DI_OVERLAP_MIN_AC, OVERLAP_MIN_SHARE as DI_OVERLAP_MIN_SHARE } from "./claims/doubleInsured";
import YieldImportPanel from "./claims/YieldImportPanel";
import FarmerSeasonHome from "./fieldmapper/FarmerSeasonHome";
import { claimPaidStages } from "./claims/claimPaidStages";
// Which claim stage codes are prevent plant — ONE rule for every claim surface (RMA stage list).
import { isPreventPlantStage as isPpStage, stageMatches as ppStageMatches } from "./claims/ppStages";
import { recordTypeLabel, recordTypeText, EVIDENCE as PR_EVIDENCE } from "./claims/productionRecord";
// The acreage a quote opens on when nothing has been reported. One constant, so
// the card, the print path and the checker cannot disagree about what was assumed.
import { DEFAULT_QUOTE_ACRES, resolveAcreageBasis, ACREAGE_SOURCE, wheatHistAcres } from "./wheat/acreageBasis";
import { unitDatabases, blendDatabases, weightedDatabaseAph, resolveQuoteYields, aphBasisNote, productionGuarantee, quoteOverDatabases, priorYearChain, plannedUnitAcres } from "./wheat/aphDatabases";
import { rdfSurchargeBranch } from "./mpciEngine";
import { FALL_APH_UNITS_SELECT, unitsFor, statusFor, resolveFallRows, fallAphLabel } from "./wheat/fallCardAph";
// The blank quote's ONE computation — the builder panel, its level grid and the
// printed sheet all price through it, so the three cannot disagree.
import { blankQuoteCoverage, blankQuotePrintHtml, BLANK_PLAN_LABEL } from "./wheat/blankQuotePrint";
import * as BFR from "./bfrStatus";
// The PRF rate lookup is a SHARED RULE because a premium may never depend on
// database row order. See src/prf/prfRateLookup.js for the measurement.
// ARC/PLC arithmetic and the canonical strategy object. The Full Strategy
// Report RENDERS these; it computes no recommendation of its own.
import { ARC_CAP_FRAC, ARC_BLOCKS_SCO, PICK_TIEBREAK, arcCapRate } from "./strategy/arcPlcRule";
// The election layer on top of that engine: the practice blend FSA requires in
// split counties. It imports the cap and the 85% factor from arcPlcRule rather
// than restating them, so there is still one copy of the money arithmetic.
import { resolvePracticeRow, irrigationAnswerKey, irrigationAnswerOf, setIrrigationAnswer, countyRead, electionLabel, practiceKeyOf, settledArcPerAcre, breakevenPaymentYield, enrolRead, enrolLabel } from "./strategy/arcPlcElection";
// The one-page election sheet. Same window.print() pattern the LRP summary and the
// wheat quote pages use — no new dependency, and the grower holds the paper.
import { buildArcPlcSheet, renderArcPlcSheet, cropYearLabel, ELECTION_WINDOWS, arcPlcPendingMessage, arcPlcYearOptionLabel } from "./strategy/arcPlcSheet";
// The report's generic cash-target ladder: the measured futures rungs translated to cash at the
// elevator's POSTED basis. One translation, no second ladder — see src/wheat/cashTargets.js.
import { wheatCashTargets } from "./wheat/cashTargets";
import { cashTargetsSection } from "./wheat/cashTargetsHtml";
import { silasYieldFactor, loadFarmElectionSummary, miCountyMap, countyCell } from "./strategy/arcPlcFarmSummary";
import { renderFarmElectionSummary, farmElectionSheetSection, comparisonSentence } from "./strategy/arcPlcFarmSummaryHtml";
// ⚠ ARC INDIVIDUAL COVERAGE IS A THIRD PROGRAM, NOT A VARIANT OF THE TWO ABOVE, and the 2026
// signup (16 Sep – 11 Dec 2026) is the first election a grower makes AFTER harvest — so they
// know their own yield before they sign, which is the only year ARC-IC can be screened on
// facts rather than on a forecast. It pays on 65% of base acres against ONE whole-farm,
// state-wide revenue; ARC-CO and PLC pay on 85% per crop per county. The panel fetches its
// own rows because the Advisor around it is scoped to one crop and one county and ARC-IC is
// neither. countyRead/resolvePracticeRow are INJECTED so the ARC-CO half of the comparison
// is the same engine this page already uses rather than a fourth copy of it.
import ArcIcScreenerPanel from "./strategy/ArcIcScreener";
import { buildFarmStrategy, STRATEGY_STATE } from "./strategy/farmStrategy";
// FULL STRATEGY REPORT V2. The engines' STRUCTURED results — gathered by wheatQuotePages'
// factSink, the function that prices the printed quote — synthesised into the document a
// grower reads. The synthesis computes no coverage; see src/strategy/reportSynthesis.js.
import { synthesizeStrategyReport } from "./strategy/reportSynthesis";
import { strategyReportHtml, technicalNotesHtml } from "./strategy/reportV2Html";
// ⚠ THE BOARD HORIZON IS resolveBoardHorizon, NOT grainBoardHorizon.
// The old helper FLOORS an expired board at 15 trading days and hands a
// contract with no delivery date the full 126 — so a dead board was still
// getting a target ladder sized against a working window it does not have.
// The corrected module existed and was called by NOTHING; these are its
// callers. `asOf` is passed explicitly because a horizon that reads the clock
// inside the calculation is not reproducible.
import { resolveBoardHorizon, BOARD_STATE, BOARD_STATE_LABEL } from "./positions/grainHorizon";
// A Notice of Loss is a claim the agent has already opened; the AIP has simply
// not written a stage record for it yet. One rule, and it takes the commodity
// map rather than typing one — see src/claimNotices.js.
import { outstandingNotices, NOTICE_TYPE_NAME, NOTICE_INTENTION_NAME,
         NOTICE_SELECT_COLUMNS } from "./claimNotices";
// The status a filed-but-unstaged Notice of Loss carries on the Claims page. It
// is deliberately NOT "Open": Open means the AIP has assigned an adjuster and
// written a stage record, and the whole point of this row is that it has not.
/* ⚠ "NOTICE FILED" MEANT TWO DIFFERENT THINGS ON ONE SCREEN.
 *
 * Kevin, 2026-10-06: "Im almost positive claims manager shows NAU adjuster when
 * on laptop but for some reason it doesnt on mobile now?" He had seen both, and
 * they were different KINDS of row wearing the same words:
 *
 *   · the STAGE label for P20X code SCT (CLAIM_STAGE_LABEL, confirmed 2026-06-15 as
 *     NAU's claim-INITIATION record) — a REAL indemnity row, status Open, WITH an
 *     adjuster. TRI ACRES LLC soybeans reads "Notice Filed / WHEELER, LENA / Open".
 *   · this STATUS, on the synthetic row we build from a P58 notice when the AIP has
 *     written no claim record at all — nothing attached, no adjuster to attach.
 *
 * On a laptop both are on screen at once and the pair reads as an inconsistency; on
 * a phone you meet one card at a time and the second kind reads as a farm whose
 * adjuster we lost. The comment at the stage filter already named TRI ACRES as the
 * colliding case without resolving the collision.
 *
 * The STAGE label is sourced from NAU's own record meaning, so OURS is the one that
 * moves. "Notice only" says exactly what it is: the notice is all we hold.
 */
const NOTICE_CLAIM_STATUS = "Notice only";
// Products that settle on an INDEX, so no adjuster ever walks the field. Measured
// on the live book 2026-10-06: every active prf (14) and lrp (4) row carries a
// blank adjuster, and every active crop row (116) carries a name — the partition
// is exact, so this is read off policy_type rather than guessed from a crop name.
const INDEX_SETTLED_TYPES = new Set(["prf", "lrp", "drp"]);
// Adjuster names come through as "LAST, FIRST"; open rows with no adjuster yet
// arrive as bare punctuation (",") — treat anything with no letters as empty.
const cleanAdjuster = (a) => (a && /[A-Za-z]/.test(a)) ? a.trim() : null;

/* ⚠ A BLANK ADJUSTER CELL HAS FOUR CAUSES AND THEY ARE DIFFERENT FACTS.
 *
 * Kevin, 2026-10-06: *"It shows adjusters for rain and hail but not NAU on mobile
 * for claims."* His read of the screen was right and the cause is NOT the carrier.
 * Measured over CY2026, live book:
 *   · NAU active `indemnities` rows: **69 of 69 carry an adjuster**. R&H: 28 of 45.
 *   · Every blank-adjuster CROP row on that screen is a NOTICE row — a filed P58
 *     with no P20X stage record — and **all 26 are NAU**, because NAU files more
 *     notices (305 vs 159) and 26 of its cells have no stage record yet.
 *   · R&H’s 18 blanks are **all prf/lrp**, which are never field-adjusted.
 * So the blanks cluster on NAU for a plumbing reason, which is exactly what the
 * comment above `noticeClaims` says was fixed for the SECTION and never for the
 * CELL. A blank beside rows that name somebody reads as missing data.
 *
 * P58 carries NO adjuster field — 28 fields, every one checked — so nothing is
 * being thrown away here; the honest fix is to say WHY the cell is empty.
 *
 * ⚠ AND THE FOURTH STATE IS NOT HYPOTHETICAL: 13 CY2025 R&H active CROP rows carry
 * a blank adjuster, one year-filter away. We do not know why those are empty, so
 * they read "not stated" — never "not yet assigned", which would be a claim about
 * the carrier’s workflow that nothing in the file supports.
 */
function adjusterCell(c) {
  const name = cleanAdjuster(c && c.adjuster);
  // `adjusterFrom` is set only where the name was resolved from ANOTHER row on the
  // same policy — a notice of loss carries no adjuster field of its own. The name
  // renders identically either way, because an agent needs to read it and pick up
  // the phone; the hover is where the difference lives.
  if (name) return { name, reason: null, why: (c && c.adjusterFrom) || null };
  if (INDEX_SETTLED_TYPES.has(String((c && c.policyType) || "").toLowerCase()))
    return { name: null, reason: "no adjuster — index settlement",
             why: "This product settles on a published index, so no adjuster inspects the field." };
  /* ⚠ THIS SAYS WHAT OUR FILE HOLDS, NEVER WHAT THE CARRIER HAS DONE.
   *
   * It first read "not yet assigned", derived from the absence of a P20X stage
   * record. Kevin, 2026-10-06: "I meant it isnt true." The AIP assigns an adjuster
   * when the notice is filed; what is missing is the RECORD, not the adjuster.
   *
   * That was an inference that fit the data perfectly and made a claim about NAU's
   * workflow that nothing in the file supports — rule zero exactly, settled by the
   * agent who sells the product rather than by more measurement. The measured half
   * stands: P58 carries no adjuster field at all (28 fields, every one checked), so
   * a notice with no stage record is production we cannot name an adjuster FOR. */
  if (c && c.status === NOTICE_CLAIM_STATUS)
    return { name: null, reason: "adjuster not in file yet",
             why: "A notice of loss has been filed and the AIP has not written a stage record, so no adjuster is named in the file yet." };
  return { name: null, reason: "adjuster not stated",
           why: "The AIP’s file names no adjuster on this claim and does not say why." };
}
import { RMA_CROPS } from "./rmaCrops";
// The exceptions screen (admin only, agent app only). See src/attention/needsAttention.js.
import NeedsAttentionView from "./attention/NeedsAttentionView";
// ONE rule for "the carrier reported acres and no dollars", shared by SpringView
// and FallView so the two cards cannot disagree about which guarantees are ours.
import { silentCoverageNote } from "./coverageDollars";
import { lookupPrfRate, rateOptions as prfRateOptions, practicesFor as prfPracticesFor,
         rateProvenance as prfRateProvenance, PRF_TYPE, PRF_TYPE_LABEL,
         SELECT_COLUMNS as PRF_RATE_SELECT } from "./prf/prfRateLookup";
// The ONE grain target engine — shared with the Model Farm order book (api/_grain-wizard.js
// requires this same file). Two engines that disagreed became one engine with two skins.
import { grainContractTech, grainFillCurve, grainTargetLadder, grainBoardHorizon, grainBreakeven, grainBoardSymForMonth, grainCloseHist, grainTechShortfall } from "./grainTargets";
// ONE rule for "what did they report to RMA?" - the Grain tool and the Claims
// Manager both ask it, so it lives in one module rather than twice inline.
import { reportedProductionForCrop, REPORTED_PRODUCTION_SELECT } from "./reportedProduction";
import { appraisalRows, summarizeAppraisals, fsnTractField, appraisalInsuredType } from "./appraisalSheet";
// The carrier's own worksheet, read out of its own text. Deterministic and checkable
// line-by-line against the document a grower holds, which is what an appraisal on a
// federal claim needs — the model path in extractSilageAppraisal is the fallback for a
// form this does not recognise, not the other way round.
import { parseAppraisalDocument, toStorageRows } from "./claims/silageAppraisal";
import { appraisalStoragePath, sameContentOnFile, appraisalOverlap, withProvenance, isMissingProvenanceColumn, withoutProvenance } from "./claims/appraisalStore";
// One map from a basis sheet's printed column header to the source slug the
// history is keyed on, shared with tools/basis-history-load.js and
// api/_howlett-basis.js so an imported sheet EXTENDS the 2021-2026 history
// instead of opening a fresh cohort beside it.
import { slugForMarket, unmappedSlug, DELIVERY_TOWN_SLUGS, slugsForTown,
         CANONICAL_MARKET_LABELS } from "./positions/basisMarketAliases";
// Live Bids (Kevin only): the price rules shared with api/_live-futures.js.
import { symbolFor as liveFutures_symbolFor, freshness as liveFutures_freshness,
         bid as liveFutures_bid, parseSymbol as liveFutures_parseSymbol } from "./liveFutures";
// A working cash target is a CASH promise; the futures order keyed into the
// elevator's system is derived arithmetic that expires the moment basis moves.
// boardToObservations is the ONLY supported way to feed this engine — it reads
// grain_basis_board (the LIVE board), never basis_sheet_observations (the photo
// archive), which is the 45-day-stale bug that shipped once already.
import { basisSheetPrompt } from "./positions/basisSheetPrompt";
// The market half of Home, defined ONCE and mounted by two pages — the agent's
// Home and the customer portal's home. None of these read farmer data; they take
// the api/market?fn=home payload and nothing else. See src/home/homeCards.jsx.
import { useHomeData, homeTheme, HomeBriefing, HomeBoard, HomePerspective,
         HomePurchasingPower, INK, MID, MUTED, LINE, CARD, UP, DOWN,
         fmtDate, boardSummaryLine } from "./home/homeCards";
// The customer's own upcoming RMA dates — sourced from rma_dates, never typed.
import { upcomingInsuranceDates } from "./home/insuranceDates";
import { extractPhotoJson } from "./positions/photoExtract";
import { targetTicketPrompt } from "./positions/targetTicketPrompt";
import { readTargetTicket } from "./positions/targetTicketRead";
import { boardToObservations, buildTargetReview, q, unq,
         parseBasisCents, sheetStampToDate } from "./positions/cashTargetBasis";
import { renderTargetSheetDocument, STRUCTURES as SHEET_STRUCTURES,
         COMMODITIES as SHEET_COMMODITIES } from "./positions/targetSheetPrint";
import { targetLifecycle, sortTargets, filterTargets, filterFacets,
         boardSymbolFor, boardClosedThrough, restingSince, targetApproach } from "./positions/targetExpiry";
// Is today's basis strong or weak for THIS delivery point, against the same
// point's own history at the same distance from delivery? Cross-crop-year and
// crop-year-equalized, and it refuses on a thin cohort.
import { scoreBasisAtLead, buildBasisStrengthGauge, normalizeObservation, aggregateBasisStrength } from "./positions/basisStrength";
// The FUTURES half of the same question, deliberately the same shape. A cash
// price is futures plus basis, so the board shows both rather than blending.
// scoreContractAtLifePoint and contractLifeOffsetDays are already imported
// below; only the two this board additionally needs are pulled in here.
import { normalizeFuturesHistoryRows, buildContractLifeFuturesGauge } from "./positions/futuresContractHistory";
// "Cash flow by month based on their sales" (Kevin, 2026-08-20). Pure and
// checked — tools/check-grain-cashflow.js covers why a committed bushel has
// three states and why they must never be added together.
import { buildMonthlyCashFlow } from "./positions/grainCashFlow";
// Silas position/action engine (ROADMAP.md §1.2/§1.2b) — the deterministic
// status/economics/market layer behind Grain Marketing's Summary tab. Pure
// modules; see src/positions/ for why grainRules.js duplicates App.jsx's own
// guarantee math rather than importing it (App.jsx exports nothing).
import { buildGrainPosition, positionBannerState, cropMatches, contractPricedBushels } from "./positions/grainPosition";
// ONE chopped-acres rule for both callers — this file's cropInsurance (the
// per-farm Marketing Tool) and buildGrainPosition (the book view + the crop-year
// continuity strip). It shipped in App.jsx alone first and the two screens
// disagreed about the same farm within the hour.
import { grainChopAdjust, choppedAcresFor, cropChopsForSilage, grainOrganicCropsPresent, grainReportedByMarketingCrop, grainCropParts, isOrganicRow, contractMatchesMarketingCrop, defaultedPracticeContracts, unassignedPracticeContracts, grainPracticesForCrop, grainContractPractice } from "./positions/grainRules";
import { buildGrainAction, resolveGrainCop } from "./positions/grainAction";
import { resolveGrainCash } from "./positions/grainCash";
import { buildForwardCurve } from "./positions/grainCurve";
import { scoreContractAtLifePoint, contractLifeOffsetDays, rankContractAmongYears } from "./positions/futuresContractHistory";
// The Summary tab's ONE view-model (src/positions/grainSummary.js). It composes
// the engines above rather than re-deriving any of them, and it is where the
// four misleading conclusions the 2026-08-28 teardown found are corrected: the
// production LABEL, the target-range STATE (a range has two boundaries and a
// next step is a third thing), the futures comparison (one population, ranked
// in YEARS, never two unlabelled percentiles), and the harvest-outlet
// arithmetic (a contract delivering in March does not house a bushel in
// October). See tools/check-grain-summary.js.
import { sessionTrend } from "./positions/grainSummary";
import { resolveGrainStorage, STORAGE_MODE } from "./positions/grainStorage";
import { classifyMarketRegime } from "./positions/grainRegime";
// The PRF review engine. Pure builders over rows this file fetches — the screen,
// tools/prf-review-probe.mjs and tools/check-prf-review.js all call the same code.
import ClaimWatchBoard from "./admin/claimWatchBoard";
import * as PRF_REVIEW from "./prf/prfReview";
import * as BOOK_CY from "./cropYear/bookCropYear";
import * as PRF_GRID from "./prf/prfGrid";
// Which campaign a 578 belongs to — shared with api/box.js so the two writers
// that disagreed about season and year now answer from one rule.
import * as CAMPAIGN578 from "./campaign578";
// When a 578 row may say "forwarded", and when an upload is the same bytes as one
// already on file — shared with api/box.js (src/fsa578Intake.js).
import * as INTAKE578 from "./fsa578Intake";
// The ONE document index: canonical storage path, content hash, index row (src/documentIndex.js).
import * as DOCINDEX from "./documentIndex";
// FSA-156EZ: the farm record's base acres, read and reconciled (src/fsa156ez.js), shown and printed per farmer.
import * as FSA156 from "./fsa156ez";
import { FsaBaseAcresPanel } from "./fsa156ezPanel";
// CCC-866 (ARC-CO / PLC election and contract): PDF reader here, parser + section inside the Base Acres panel.
import { readCcc866Pages } from "./ccc866Pdf";
import * as PRF_DRAFT from "./prf/prfQuoteDraft";
// Coverage-level comparison and the history matrix behind it. Same engine as the
// quote above them — check-prf-compare asserts the drawer ties to it year by year.
import * as PRF_CMP from "./prf/prfCompare";
// The quote engine's own rules, imported rather than restated — the overlap
// refusal has to be the SAME rule the engine throws on, or the screen and the
// engine disagree about what RMA will write.
import * as PRF_QUOTE_RULES from "./prf/prfQuote";
// Selectable historical windows + the two replay loss ratios. The interval
// ratio is allocation-independent BY CONSTRUCTION — see that file's header.
import * as PRF_IH from "./prf/prfIntervalHistory";
import * as PRF_IS from "./prf/prfIntervalStatus";
import * as PRF_EM from "./prf/prfEvidenceMatrix";
// The renewal quote — next year's structure priced against next year's tables.
// It takes `lookupPrfRate` as an argument rather than importing it, so there can
// never be a second rate rule; see the header of prfRenewalQuote.js.
import * as PRF_RQ from "./prf/prfRenewalQuote";
// The printable quoting package. Formats what the page already priced — it
// computes nothing, so a sheet cannot disagree with the screen it came from.
import * as PRF_PRINT from "./prf/prfQuotePrint";
// Every print window gets a "Back to SILAS" bar (hidden when printed) — see printBackBar.js.
import { writePrintWindow, withPrintBackBar } from "./printBackBar";
// RH-5237, the PRF lease certification. Pure — returns an HTML string the caller
// opens, the same shape prfQuotePrint uses, so a checker can render it and read the
// output. That is what caught a CONFIRM line printing with no reason on it.
// The PRF renewal roster. ONE engine for the Coverage Advisor's PRF Renewals
// list and File Management's PRF pipeline board — two screens asking the same
// question, which is exactly the shape that grows a second copy if you let it.
import * as PRF_RENEW from "./prf/prfRenewals";
import { geomCentroidAcres as _sharedCentroidAcres } from "./geoArea";
import { buildDrpPosition } from "./positions/drpPosition";
// THE DRP decision rule — one home, four callers. The page must not re-derive
// any of it: the legacy 1-10 scorer below exists in two copies and that is
// precisely how it drifted from the number it was supposed to explain.
import { latestEstimatesFromRows as drpLatestMonthEstimates, mergeForwardPrices as drpMergeForwardPrices, describeEstimate as drpDescribeEstimate } from "./drpMonthEstimate";
import { drpDecide as drpDecideRule, buildFloorHistory as drpFloorHistory, drpQKey as drpQKeyRule, parseDrpQuarter as drpParseQuarterRule, DEFAULT_THRESHOLDS as DRP_DECISION_THRESHOLDS, previousDecisionInput as drpPreviousDecisionInput } from "./drpDecision";
// Only the endorsement quarter-key rule is needed here now. coverageForCell()
// answers "is THIS FARM covered", and this page has no farm — that belongs to
// Farm Fit. The rule stays in the module for it; importing it here invited the
// farm-level verdict this page had no standing to print.
import { endorsementQKey as drpEndorsementQKey } from "./drpCoverage";
import { drpResolveYieldAdj, drpRevenueGuarantee, drpP28Indemnity, drpQuarterYieldSummary, drpQuarterActualOf, stateFipsOf as drpStateFipsOf } from "./drpYieldAdjust";
import { renderDrpAlert } from "./drpAlertEmail";
// The run date, as a local ISO day. The decision needs a CLOCK; handing it the
// board's own newest date is the defect that made staleness undetectable.
const drpTodayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
import { buildDrpAction } from "./positions/drpAction";
import { buildCropYearContinuity } from "./positions/grainCropYearContinuity";
// THE Cost-of-Production rules. Extracted from GrainMarketingView so the SILAS
// shadow cost estimator can call the same economics instead of reproducing them
// (Farm Economics Increment 2A). Aliased to *Rule because thin delegations below
// keep the original in-component names and signatures.
import {
  COP_CATEGORIES, COP_DEFAULTS, COP_FIELD_OP_KEYS, insurancePremiumPerAcreFrom,
  copCreditTotal as copCreditTotalRule, copGrossTotal as copGrossTotalRule,
  copVal as copValRule, copDefault as copDefaultRule, copCats as copCatsRule,
  copPasses as copPassesRule, copFieldOpsTotal as copFieldOpsTotalRule,
  copMachMethod as copMachMethodRule, copLine as copLineRule,
  copSigned as copSignedRule, copSum as copSumRule,
} from "./copRules";
// A BILL IS NOT A PREMIUM. `net_premium` ties to the AIP statement exactly; what
// the grower is BILLED is that plus administrative fees minus the claim credits
// the AIP already applied. Anchor Farms read $15,439 against a bill of $8,178
// and the whole gap was a prevent-plant indemnity nothing on screen mentioned.
import { buildBilling as buildBillingRule, billingRowsForTab } from "./billing";
// What the grower has actually PAID (M13 P12) and what that leaves outstanding.
// One rule, two callers — the farmer card and Premium Analytics' outstanding tab
// must not disagree about what one farm owes.
import { policyOutstanding, paymentTypeLabel, nonCropBilledByPolicy,
         buildBookOutstanding, rollUpByFarmer, outstandingTotals,
         BILLED_BASIS, agentLabel, agentsOf, narrowToAgent, narrowToYear, yearsOf,
         UNATTRIBUTED } from "./premiumOutstanding";
// The WNY corn yield model. It USED to live in this file, which meant the only
// thing that ever ran it was this page — and mi_model_snapshot, which the daily
// email brief renders, was only as fresh as the last time someone opened the
// Yield tab. api/_mi-snapshot.js now runs the identical code on a cron.
// One model, two callers: keep it that way (see src/miYieldModel.js).
import {
  MI_COEF, MI_CORN, MI_CORN_PLANTING, MI_SEASON, MI_REGIONS, miFilterResult, MI_MODEL_VERSION,
  MI_STAGE_AT, MI_STAGE_HAS_TABLE, MI_MODEL_COUNTIES, MI_WNY_FIPS, MI_GRID_STATE_FILTER, backtestCornNowcastVsSurvey,
  MI_MIN_YIELD_YEARS, MI_THIN_YIELD_YEARS, miCountyIsThin,
  buildCornNowcastChartSvg, buildCornSurveyTimeSeries, buildFieldPulse, computeCornMidWeekNowcast,
  computeCornStaleAdjustedAnchor, getCornFPD, miBuildSurveyPace, miComputeYield,
  miDefaultLab, miDiseasePressure, miEarlyStress, miGridShadowWeather,
  miGridWxPanels, miPulseDoy, miR1, miR3,
  miReviveDates, miRollup, miSnapshotDay, miSnapshotFromResult,
} from "./miYieldModel";
// The Oct 10 freeze — one rule shared with api/_mi-snapshot.js and the brief.
import { miFreezeDate, miMayWriteDaily, miIsFrozen, miClipToFreeze, miPickFinalCall, miScoreFinalCall } from "./miFinalCall";
// The acreage half of the production forecast — the shadow challenger and the
// measured error band around the shipped 5-year-mean rule. Same file both
// callers read (api/_mi-snapshot.js is the other), because two copies of an
// acreage rule is how the recorded number and the rendered number drift apart.
import { acreageBand } from "./acreageShadow";
import { amsClassReleaseText, nextAmsClassReleaseText, etDateIso as usdaEtDateIso } from "./calendar/usdaReleaseCalendar";
// The DRP card's quarter rules (active vs all-time totals, awaiting payment,
// distance to trigger, season summary, covered-milk limit) — src/drpCardRules.js.
import { drpQuarterEndIso, drpCoverageTotals, drpIsActiveEnd, drpQuarterStatus, drpQuarterVisible, drpTriggerDistance, drpTriggerDistanceText, drpSeasonSummary, drpCoveredMilkLimit } from "./drpCardRules";
// Weather Records — see WeatherRecordsView. The rules are shared with
// api/_wx-evidence.js so the page and the printed sheet cannot disagree.
import * as WX from "./weather/wxEvidence";
import * as WXS from "./weather/wxSeason";
import * as WXP from "./weather/wxPrf";
import * as WXN from "./weather/wxNext";
import * as WXF from "./weather/wxField";
import * as RMAPW from "./rmaPlantingWindow";
import LRPCogSignalView from "./lrp/LrpPushSignalView";
import HarvestPaceCard from "./markets/HarvestPaceCard";
// The farm coverage statement for lenders (src/farm/lenderStatement.js) and the
// livestock position it carries (src/farm/livestockStatement.js).
import { livestockPosition, livestockEstimates, drpQuarterOf as lsDrpQuarterOf } from "./farm/livestockStatement";
import { lenderStatement, lenderStatementHTML, baseFromModels as lenderBaseFromModels, silasCornFactors } from "./farm/lenderStatement";
import { farmIds as farmPageIds } from "./farm/farmPageData";
import * as CCC866 from "./ccc866";
import { lrpProducerShare as sharedLrpProducerShare, BASIS_MEASUREMENT as LRP_BASIS, BAND_MID_LB as LRP_BAND_MID, resolveInputAnimal as lrpResolveInputAnimal, ownershipCostsBeyondFeed as lrpOwnershipCosts, cogPerLb as lrpCogPerLb, SCENARIOS as LRP_SCENARIOS, endorsementTimingFit as lrpTimingFit } from "./lrp/lrpPush";
// Marketing Plan — schedule (the WHEN) that consumes the target engine (the WHERE).
const STATE_FIPS = { NY:"36", PA:"42", MD:"24", OH:"39", IN:"18" };              // farmer.state (2-letter) → RMA FIPS
const FIPS_STATE = { "36":"NY", "42":"PA", "24":"MD", "39":"OH", "18":"IN" };      // RMA FIPS → 2-letter
// A wheat volatility still marked "estimate" that is EXACTLY RMA's released CY2027 figure for the
// unit's state is called "released" — see WHEAT_2027_VOLATILITY_RELEASE. Any other status (an RMA
// feed row, a typed figure), any other number, and an unknown state are left exactly as they are.
function wheatVolReleased(status, v, stateFips) {
  const rel = WHEAT_2027_VOLATILITY_RELEASE;
  return status === "estimate" && rel && MPCI_WHEAT_YEAR === rel.cropYear && stateFips && rel.states.includes(FIPS_STATE[stateFips])
    && Math.abs(Number(v) - rel.volatility) < 1e-9 ? "released" : status;
}
const stFips = s => STATE_FIPS[(s||"NY").toUpperCase()] || "36";
// `prac` is optional: with it, this also asks whether RMA offers THAT practice here, so an
// organic quote in a county with no organic offer fails visibly instead of quoting
// conventional rates under an organic heading.
const wheatCountyInSet = (fips, county, prac) => (WHEAT_COUNTIES_BY_STATE[fips] || []).includes((county||"").toUpperCase())
  && (!prac || prac === "003" || wheatPractices2027(fips, (county||"").toUpperCase()).includes(prac));
// The engines the blank quote prices with, handed to src/wheat/blankQuotePrint.js
// (import-free, so its checker drives the shipped source on the real rate tables).
const WHEAT_BLANK_ENG = { quoteRP: quoteWheatRP2027, quoteArea: quoteAreaRP2027, countyInSet: wheatCountyInSet };
// ── RMA practice code for a crop_coverage row ────────────────────────────────
// Everything about a quote forks on this: RMA publishes a SEPARATE base rate, coverage
// differential, unit discount, T-yield and price election per practice. Organic is not
// conventional with a factor on top — for 2026 wheat the certified-organic election is
// $11.16 against $5.76, so getting this wrong is a ~2x error on the guarantee.
//
// `practice_code` is the truth and is preferred whenever it's set. The label is the
// fallback, and it is LOSSY: normalizePractice() collapses certified (713) and
// transitional (714) into one string "Organic", and those two are priced differently
// ($11.16 vs $5.76). So a label-only organic row resolves to 713 and is FLAGGED — the UI
// shows the practice on the quote so the agent confirms it rather than trusting a guess.
const WHEAT_PRACTICE_CODES = ["003", "002", "713", "714"];
function practiceCodeOf(row) {
  const c = String((row && row.practice_code) || "").trim();
  if (WHEAT_PRACTICE_CODES.includes(c)) return c;
  if (c === "702") return "713";      // organic certified irrigated — no wheat table; rate as certified non-irr
  if (c === "712") return "714";
  const p = String((row && row.practice) || "").toUpperCase();
  if (p.includes("TRANSITION")) return "714";
  if (p.includes("ORGANIC")) return "713";
  if (p === "IRRIGATED" || p === "IRR") return "002";
  return "003";
}
// True when the code had to be inferred from the lossy label — the quote says so.
const practiceInferred = row => !WHEAT_PRACTICE_CODES.includes(String((row && row.practice_code) || "").trim());
// A quotable unit is (county x practice), NOT county. Every one of the five organic wheat
// growers in the book carries conventional AND organic ground in the SAME county —
// Jeffres runs Wyoming conventional 673 ac / APH 99 beside Wyoming organic 163 ac / APH 75,
// two rate tables, two price elections, two APHs, two option strings (Martin: TA on the
// conventional, YA on the organic). Keying the quoter by county alone kept whichever row
// Supabase returned first and silently dropped the other.
const wheatUnitKey = r => (r && r.county ? String(r.county).toUpperCase() : "") + "|" + practiceCodeOf(r);

// ── LAST YEAR'S COVERAGE, PER LAYER — ONE RULE, TWO CALLERS ──────────────────
// The 2026-to-2027 side-by-side page and the Full Strategy Report's page one both
// state what a grower carried last year, and re-deriving it in two places is how
// two surfaces of one document come to disagree about it.
//
// ⚠ COVERAGE IS DERIVABLE FROM THE PRICE; PREMIUM IS NOT. Every layer's coverage is
// a band of expected revenue — MPCI 0 to the elected level, SCO the level to its
// top, ECO its own two points — so with the approved yield, the election and RMA's
// published price they are arithmetic, and it is the same arithmetic
// `quoteAreaRP2027` rates against. The PREMIUM needs the prior year's rate tables,
// which are not in this tree; modelling it would put a ~17.5% error beside
// penny-exact figures on a document a grower signs.
//
// A BILLED FIGURE ALWAYS WINS, so a policy the AIP actually keyed is untouched. AND
// AN ENDORSEMENT HE DID NOT CARRY GETS NOTHING — deriving a band for an absent layer
// would invent coverage he never bought, which is the direction that flatters the
// comparison.
function wheatPriorCoverage(cur, er, cl, bands, hasSco, hasEco) {
  const c = cur || {};
  return {
    mc: c.mc != null ? c.mc : (er != null ? er * cl : null),
    sc: c.sc != null ? c.sc : ((hasSco && er != null && bands) ? er * Math.max(0, bands.scoTop - cl) : null),
    ec: c.ec != null ? c.ec : ((hasEco && er != null && bands) ? er * Math.max(0, bands.ecoTop - bands.ecoBottom) : null),
  };
}
// PLANNED-ACRES KEY. wheatUnitKey is COUNTY|PRACTICE and is deliberately farm-agnostic — it
// groups one farm's coverage rows. A typed planned acreage is not farm-agnostic: the BATCH
// printer walks many farms through the same wheatQuotePages, and two growers both farming
// LANCASTER|003 would otherwise share one typed figure, silently pricing farm B's premium on
// farm A's acres. The policy id scopes it — every unit row on both the screen and the print
// path is built by the same `{...(g.cur || g.prev)}` spread, so r.policy_id agrees across them.
const wheatPlannedKey = r => String((r && r.policy_id) || "") + "|" + wheatUnitKey(r);
// EVERY PER-UNIT AGENT CHOICE IS SCOPED THE SAME WAY (BUG-014). Planned acres
// were given a policy scope in September; the APH-option ticks, the practice
// override and the seed marker were left on the farm-agnostic COUNTY|PRACTICE
// key and shared across growers.
//
// MEASURED on the CY2027 wheat book: 35 farms share LIVINGSTON|, 25 share
// MONROE|, 20 GENESEE|, 17 ONTARIO|, 15 ORLEANS| and 15 WYOMING|. So ticking
// TA on one Livingston grower set it for thirty-four others, and — because the
// seed guard never re-runs for a key it has already seeded — the next grower
// opened showing the FIRST one's elections instead of his own.
//
// Same rule, same reason, same shape as the acreage key it sits beside.
const wheatOvrKey = wheatPlannedKey;

// ── THE QUOTED CROP YEAR IS THE ONLY ACREAGE THAT MAY PRICE A PREMIUM ────────
// A wheat unit row is assembled as `{ ...(g.cur || g.prev) }` — the CURRENT crop
// year where the grower carries one, and THE PRIOR YEAR'S ROW WHOLESALE where he
// does not. That collapse is right for the APH, the county and the practice, and
// it is wrong for exactly one field: `acres`. A CY2027 card with no CY2027
// coverage was reading CY2026's reported acreage as though the grower had
// reported it for the year being quoted, so `premiumBasis` went true and MPCI,
// SCO and ECO all priced against acres nobody has reported for that crop year.
//
// `_curAcres` is stamped at assembly from `g.cur` ALONE and is the only figure
// this rule will return. It is a separate field rather than a corrected `acres`
// because `acres` is still the right number for the backward-looking "2026
// actual" column and for the prior-year reference line — those are true
// statements about a year that HAS closed.
//
// ⚠ IT RETURNS 0, NEVER A FALLBACK. Prior-year acreage, production-history
// acreage and a nearest-crop-year pick are each a figure a human never reported
// for the quoted year, and this repo has now paid twice for a defaulted acre:
// once at `AC || 100` in the book runner and once at `+acres || 1` in the
// coverage grid. Missing stays missing; the caller withholds the premium.
const wheatQuotedYearAcres = row => (row && +row._curAcres > 0 ? +row._curAcres : 0);

// Load D3 from CDN for MapView geo projection
const d3Promise = new Promise((resolve) => {
  if (window.d3) { resolve(window.d3); return; }
  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/d3@7/dist/d3.min.js';
  script.onload = () => resolve(window.d3);
  script.onerror = () => resolve(null);
  document.head.appendChild(script);
});

// Top-level DRP crop year derivation (used by MapView and Dashboard)
function deriveDrpCropYear(effectiveDate) {
  if (!effectiveDate) return null;
  const iso = String(effectiveDate).match(/^(\d{4})-(\d{2})/);
  const mdy = String(effectiveDate).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  let y, m;
  if (iso) { y = parseInt(iso[1]); m = parseInt(iso[2]); }
  else if (mdy) { y = parseInt(mdy[3]); m = parseInt(mdy[1]); }
  else return null;
  return m >= 7 ? y + 1 : y;
}

// Identity of a single DRP endorsement, for deduping the same endorsement when
// it is carried under two crop-year policy rows.
//
// It must NOT use end_num or end_date. `end_num` is only a positional counter
// the sync recomputes per policy (buildDrpRows writes `idx + 1`), and `end_date`
// is never written by the sync at all — so the old `endDate|endNum` key read as
// "|1", "|2", "|3" … identically for EVERY crop year. Both dedups then collapsed
// a farmer's CY2026 endorsements into their CY2025 namesakes, and because the
// dashboard keeps the LOWEST crop year the 2026 book disappeared: the Agency
// Dashboard banner showed $9,124 (one surviving endorsement) against $1,900,999
// actually in the table, 2026-08-05.
//
// These five fields are what a real endorsement IS. The same endorsement seen
// under two policy rows matches on all of them; no two distinct endorsements
// share them (verified: 260 unique keys across all 260 book DRP rows).
// Accepts either the mapped camelCase object or a raw Supabase row.
function drpEndKey(e) {
  if (!e) return "";
  const v = (camel, snake) => { const x = e[camel] ?? e[snake]; return x == null ? "" : String(x); };
  return [v("effectiveDate", "effective_date"), v("quarter", "quarter"), v("type", "type"),
          v("declaredMilkProd", "declared_milk_prod"), v("totalPremium", "total_premium")].join("|");
}

// Which policy types roll to the next reinsurance year on July 1. Livestock
// (DRP/LRP) do; crops, PRF, everything else stay on the calendar/RMA crop year.
// This is why a dairy farmer's DRP can read CY2027 in July while their corn,
// wheat and PRF are all still CY2026. Keep this the single source of truth —
// don't scatter `type === 'drp'` checks through the crop-year logic.
const LIVESTOCK_POLICY_TYPES = new Set(["drp", "lrp"]);
function isLivestockPolicyType(t) { return LIVESTOCK_POLICY_TYPES.has(String(t || "").toLowerCase()); }

// ─────────────────────────────────────────────────────────────────────────────
// DEMO ACCOUNTS
// ─────────────────────────────────────────────────────────────────────────────
// A demo farmer is a presentation-only account (e.g. SPARTAN INSURANCE FARMS,
// cloned from a real customer for prospect meetings). It is flagged in the DB via
// metadata.demo === true. Demo farmers are deliberately kept OUT of the main
// `farmers` array so they NEVER feed any book-of-business / dashboard / by-AIP
// aggregate. They are injected ONLY into farmer detail (lookupFarmers) and the
// portal of a user granted one. Their DB rows use non-sync policy numbers
// (DEMO-*) so M13/NAU syncs never own or wipe their child rows. See loadData().
function isDemoFarmer(f) {
  return f?.metadata?.demo === true;
}

// Map a US state to its primary Federal Milk Marketing Order (FMMO) number, used
// to surface order-specific Statistical Uniform Price + PPD (ppd table.order_no).
// NY = Northeast (Order 1); MI = primarily Mideast (Order 33). Component prices
// (butterfat/protein $/lb) are announced NATIONALLY and are the same across orders.
const STATE_FMMO_ORDER = {
  NY: { order: "001", name: "Northeast" },
  MI: { order: "033", name: "Mideast" },
  PA: { order: "001", name: "Northeast" }, // dairy concentrated in SE/central PA (Order 1); western PA is Mideast
  MD: { order: "001", name: "Northeast" },
};

// ─────────────────────────────────────────────────────────────────────────────
// RESPONSIVE HOOK
// ─────────────────────────────────────────────────────────────────────────────
// ── Desktop "mobile preview" override ────────────────────────────────────────
// When ON, every useIsMobile() consumer returns true regardless of window width,
// so the whole app renders its phone layout — shown inside a 390px device frame
// (MobilePreviewFrame) so an admin can see exactly what a mobile user sees, the
// same way portal preview shows what another user sees. Persisted in localStorage;
// toggled via a floating admin-only button that fires the `cloverag:forcemobile`
// event so mounted hooks flip live without a reload.
let __forceMobile = (() => { try { return localStorage.getItem("cloverag_force_mobile") === "1"; } catch (e) { return false; } })();
function getForceMobile() { return __forceMobile; }
function setForceMobile(on) {
  __forceMobile = !!on;
  try { localStorage.setItem("cloverag_force_mobile", on ? "1" : "0"); } catch (e) {}
  try { window.dispatchEvent(new Event("cloverag:forcemobile")); } catch (e) {}
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => __forceMobile || window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(__forceMobile || window.innerWidth < 768);
    window.addEventListener("resize", handler);
    window.addEventListener("cloverag:forcemobile", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("cloverag:forcemobile", handler);
    };
  }, []);
  return isMobile;
}

// ─────────────────────────────────────────────────────────────────────────────
// SUPABASE CLIENT
// ─────────────────────────────────────────────────────────────────────────────
const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY  = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Which build is running. Stamped onto client_errors rows so a crash report says
// which deploy produced it — without it, "is this already fixed?" is unanswerable.
// vercel.json's buildCommand passes $VERCEL_GIT_COMMIT_SHA through; a plain local
// `npm run build` has no SHA and reports "dev".
const APP_VERSION = (process.env.REACT_APP_GIT_SHA || "dev").slice(0, 7);

// ── Supabase Auth session management ──
// We use Supabase GoTrue (Auth) REST API directly — no SDK dependency.
// On login, we get an access_token (JWT) which is used for all subsequent
// API calls. This JWT, combined with RLS policies, ensures only authenticated
// users can read/write data.
let _supabaseAccessToken = null;
let _supabaseRefreshToken = null;
let _refreshTimer = null;
// The one in-flight refresh, shared by every caller — see supabaseRefreshSession.
let _refreshInFlight = null;
// The exact refresh-token string a refresh has already conclusively rejected.
// restoreSession runs twice on a cold boot (App's effect, then LoginScreen's),
// which is why the Howlett incident shows TWO 400s 8 ms apart instead of one.
// Retrying a string we already know is dead can only produce a second 400.
let _deadRefreshToken = null;

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW-AS — the admin diagnostic, backed by the farmer's REAL session.
//
// Preview used to render the portal with the ADMIN's token and trim the results
// in JS. Every customer table carries an is_admin() policy beside its farmer
// policy, so the admin policy answered and the client subtracted rows back down.
// Subtraction can only ever show too MUCH — it can never reveal what the
// farmer's own RLS would have DENIED, so a customer seeing LESS than they should
// was structurally invisible. That is the Panek Field Mapper call (2026-08-13):
// the map was right for the agent, empty for the grower, and preview agreed with
// the agent.
//
// `/api/portal-invite?fn=preview` now mints a real short-lived session for the
// target user. While it is held, EVERY Supabase read and write, and every /api/*
// call, goes out as that farmer — their RLS, their grants, their is_admin()=false.
//
// sessionStorage, not localStorage and not a bare module variable:
//   • per-TAB and dies with the tab, so it never leaks into the admin's next
//     visit the way a localStorage token would;
//   • it must survive exactly ONE reload, because entering preview reloads the
//     page on purpose. Without that reload the admin's already-fetched `farmers`
//     and the whole _sbCache are still in memory, and preview would render ADMIN
//     data through a farmer token — precisely the bug it exists to catch.
// Refreshing therefore KEEPS you in preview (the orange frame and Exit button
// are always on screen); Exit is what ends it.
const PREVIEW_TOKEN_KEY = "cloverag_preview_token";
const PREVIEW_META_KEY  = "cloverag_preview_meta";
let _previewAccessToken = null;

function ssGet(key) {
  try { return (typeof window !== "undefined" && window.sessionStorage) ? window.sessionStorage.getItem(key) : null; }
  catch (e) { return null; }
}
function ssSet(key, value) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    if (value == null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch (e) { /* storage disabled — preview simply won't persist */ }
}

// The impersonated access token, or null when not previewing.
function getPreviewToken() {
  if (!_previewAccessToken) {
    const stored = ssGet(PREVIEW_TOKEN_KEY);
    if (stored) _previewAccessToken = stored;
  }
  return _previewAccessToken;
}
function isPreviewSession() { return !!getPreviewToken(); }
function getPreviewMeta() {
  try { const m = ssGet(PREVIEW_META_KEY); return m ? JSON.parse(m) : null; } catch (e) { return null; }
}
// Enter/exit both hard-reload: see the sessionStorage note above.
function beginPreviewSession(accessToken, meta) {
  _previewAccessToken = accessToken;
  ssSet(PREVIEW_TOKEN_KEY, accessToken);
  ssSet(PREVIEW_META_KEY, JSON.stringify(meta || {}));
  try { sbCacheClear(); } catch (e) {}
  window.location.reload();
}
function endPreviewSession() {
  _previewAccessToken = null;
  ssSet(PREVIEW_TOKEN_KEY, null);
  ssSet(PREVIEW_META_KEY, null);
  try { sbCacheClear(); } catch (e) {}
  window.location.reload();
}

// The REAL signed-in session's email, ignoring any preview. Used only to decide
// which app shell renders and whether preview may be entered at all — never for
// data access, which must always follow the effective (possibly previewed)
// identity below.
function getSessionEmail() {
  let token = _supabaseAccessToken;
  if (!token && typeof window !== "undefined" && window.localStorage) {
    token = window.localStorage.getItem("cloverag_access_token");
  }
  if (!token) return null;
  try { return (JSON.parse(atob(token.split(".")[1]))?.email || "").toLowerCase() || null; }
  catch (e) { return null; }
}
function isRealAdminSession() {
  const email = getSessionEmail();
  return !!email && ADMIN_EMAILS.includes(email);
}

function getAuthHeaders() {
  // Preview wins outright: while impersonating, every request must go out as the
  // farmer or the diagnostic lies.
  const preview = getPreviewToken();
  if (preview) {
    return { apikey: SUPABASE_KEY, "Content-Type": "application/json", Authorization: `Bearer ${preview}` };
  }
  // Use the in-memory JWT, or hydrate it from localStorage on a fresh page
  // load. We INTENTIONALLY do NOT fall back to the anon key as a Bearer
  // token — the prior fallback let unauthenticated callers reach the data
  // layer with full anon privileges, which combined with overly-permissive
  // RLS exposed every customer table. RLS is now locked down to require an
  // authenticated JWT on every customer table; if for any reason a fetch
  // fires without a token, omit the Authorization header so Supabase
  // rejects it cleanly (401) instead of treating it as a valid anon read.
  if (!_supabaseAccessToken) {
    const stored = typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem("cloverag_access_token")
      : null;
    if (stored) _supabaseAccessToken = stored;
  }
  const headers = {
    apikey: SUPABASE_KEY,
    "Content-Type": "application/json",
  };
  if (_supabaseAccessToken) headers.Authorization = `Bearer ${_supabaseAccessToken}`;
  return headers;
}

// Auth header for /api/* endpoints that gate on the user's Supabase session
// (api/_auth.js → requireAdmin). Returns the user's access token only —
// never the anon key, since the backend rejects anon-key bearers.
function getApiAuthHeaders() {
  // In preview the farmer's token goes to /api/* too, so an admin-gated endpoint
  // returns the 401/403 the FARMER would get. That is the point: a portal screen
  // quietly reaching an admin endpoint has to fail here the way it fails for them.
  const preview = getPreviewToken();
  if (preview) return { "Content-Type": "application/json", Authorization: `Bearer ${preview}` };
  if (!_supabaseAccessToken && typeof window !== "undefined" && window.localStorage) {
    const stored = window.localStorage.getItem("cloverag_access_token");
    if (stored) _supabaseAccessToken = stored;
  }
  const headers = { "Content-Type": "application/json" };
  if (_supabaseAccessToken) headers.Authorization = `Bearer ${_supabaseAccessToken}`;
  return headers;
}

// Fetch wrapper for admin /api/* calls. If the access token expired mid-session
// the backend returns 401 ("Admin auth required"); refresh the session once and
// retry so a stale token doesn't surface as a spurious permission error.
async function adminApiFetch(url, opts = {}) {
  const build = () => ({ ...opts, headers: { ...getApiAuthHeaders(), ...(opts.headers || {}) } });
  let res = await fetch(url, build());
  if (res.status === 401) {
    const ok = await supabaseRefreshSession();
    if (ok) res = await fetch(url, build());
  }
  return res;
}

// ── READING AN /api/* RESPONSE WITHOUT SHOWING THE AGENT A PARSE ERROR ───────
//
// ⚠ NOT EVERY RESPONSE IS JSON, AND `r.json()` ON AN HTML BODY THROWS SOMETHING THE
// READER CANNOT ACT ON: `Unexpected token '<', "<!doctype "... is not valid JSON`.
// That is what reaches the screen whenever the platform answers instead of the handler
// — a Vercel 502/504 error page, a request made while a deploy is swapping, an auth
// redirect, or a route that does not exist in this build. Seen live on the FSA-426
// panel, where it was the entire error message an agent got.
//
// Three rules, the same ones `api/_jd.js`'s boundary already follows in the other
// direction:
//   · A HANDLER'S OWN `{error}` IS SHOWN, because those are written for the reader.
//   · A NON-JSON BODY IS NEVER ECHOED. It is a platform page or a stack trace, and
//     what the reader needs is the status and what to do, not the HTML.
//   · IT NAMES THE STATUS, so "try again" and "this is broken" can be told apart.
//
// Returns the parsed body; THROWS with a readable message otherwise, so existing
// `catch (e) { setErr(e.message) }` call sites need no other change.
async function apiJson(res, what = "the server") {
  const text = await res.text().catch(() => "");
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (body && typeof body === "object") {
    if (!res.ok) throw new Error(body.error || body.message || `${what} returned HTTP ${res.status}`);
    return body;
  }
  // Not JSON at all. Say what happened and what to do; never print the body.
  if (res.status === 404) throw new Error(`${what} has no such endpoint in this build (HTTP 404).`);
  if (res.status === 401 || res.status === 403) throw new Error(`Not signed in as an admin (HTTP ${res.status}).`);
  if (res.status >= 500 || res.status === 0) throw new Error(`${what} is not responding (HTTP ${res.status}) — it may be mid-deploy. Try again in a moment.`);
  throw new Error(`${what} returned something that is not data (HTTP ${res.status}) — try again in a moment.`);
}

// Admin allowlist — MUST stay in sync with api/_auth.js ALLOWED_ADMIN_EMAILS and
// the app_admins table (which drives the is_admin() RLS gate). This frontend copy
// only decides which app shell to render (agent Dashboard vs farmer Portal) and
// which admin-only nav items show; the DB enforces real access regardless.
const ADMIN_EMAILS = [
  "kdkeenan10@gmail.com",
  "kkeenan@cloverag.com",
  "mmeisenzahl@cloverag.com",     // Mike's actual login
  "mmeisenzahl@spartancrop.com",  // kept in case of a second account
  // office@cloverag.com removed from admin 2026-06-25 (Kevin: admin = only Kevin +
  // Mike). It's still the notification "from"/contact address — just not full-app admin.
];

// Decode the EFFECTIVE user's email from the Supabase JWT (no extra request).
// "Effective" = the previewed farmer while preview is on, otherwise the real
// session. Everything that decides what DATA a screen shows must use this, so
// that preview reproduces the farmer's view — including isCurrentUserAdmin()
// below, which goes false in preview and correctly strips admin affordances out
// of shared components. The app-shell routing uses isRealAdminSession() instead.
function getCurrentUserEmail() {
  let token = getPreviewToken() || _supabaseAccessToken;
  if (!token && typeof window !== "undefined" && window.localStorage) {
    token = window.localStorage.getItem("cloverag_access_token");
  }
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return (payload?.email || "").toLowerCase() || null;
  } catch {
    return null;
  }
}

function isCurrentUserAdmin() {
  const email = getCurrentUserEmail();
  return !!email && ADMIN_EMAILS.includes(email);
}

// LRP INDEMNITIES ARE ADMIN-ONLY (Kevin, 2026-10-21: "LRP indemnities can be admin
// only for now"). Both the AIP's own figure (payable_indemnity, LRP claim rows) and
// every figure we compute (coverage − AEV, the CME estimate) render only for a real
// admin. It is the EFFECTIVE identity, so it is false in the portal AND in preview-as
// a farmer. Where it is false the column / section is ABSENT — never a dash, which
// reads as "nothing was paid". Coverage, premium and endorsement details are not
// affected. tools/check-lrp-indemnity-admin.js holds every caller.
function canSeeLrpIndemnity() {
  return isCurrentUserAdmin();
}

// The logged-in user's Supabase user id (JWT `sub`) — needed when a portal user
// inserts owner-scoped rows (e.g. price_alerts) whose RLS WITH CHECK = auth.uid().
function getCurrentUserId() {
  let token = getPreviewToken() || _supabaseAccessToken;
  if (!token && typeof window !== "undefined" && window.localStorage) {
    token = window.localStorage.getItem("cloverag_access_token");
  }
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split(".")[1]))?.sub || null;
  } catch {
    return null;
  }
}

// localStorage can throw outright (Safari private mode, storage disabled, quota).
// Auth must never die on that, so every access goes through these two.
function lsGet(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch (e) { return null; }
}
function lsSet(key, value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, value);
  } catch (e) { /* nothing we can do; the in-memory copy still works this tab */ }
}

// Adopt a token pair returned by GoTrue (password login, refresh, or magic link):
// memory + localStorage + the next expiry timer, in one place so no path can
// update two of the three and leave the session half-rotated.
function adoptSession(data) {
  if (!data || !data.access_token) return false;
  _deadRefreshToken = null;          // a live session again — forget the old verdict
  _supabaseAccessToken = data.access_token;
  lsSet("cloverag_access_token", data.access_token);
  if (data.refresh_token) {
    _supabaseRefreshToken = data.refresh_token;
    lsSet("cloverag_refresh_token", data.refresh_token);
  }
  scheduleTokenRefresh(data.expires_in || 3600);
  return true;
}

async function supabaseSignIn(email, password) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: "POST",
    headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error_description || err.msg || "Login failed");
  }
  const data = await res.json();
  adoptSession(data);   // memory + localStorage + refresh timer
  flushAuthBreadcrumbs();  // report why they had to type a password, now that we can
  return data;
}

// ── Refresh-token rotation, made safe for many tabs and many callers ─────────
// GoTrue ROTATES the refresh token on every use: the old one is consumed, and
// reusing it returns 400 `refresh_token_already_used`. Three things in the old
// code broke that contract and dumped signed-in farmers onto the login screen
// (Howlett, 2026-08-03 09:59 UTC — two 400s 8 ms apart, then he retyped his
// password):
//   1. No single-flight. Boot, every authedFetch/adminApiFetch 401 retry, and
//      the expiry timer could each POST the SAME token concurrently. One won;
//      every other caller got a 400.
//   2. The timer read only the in-memory copy, so a token rotated by ANOTHER
//      TAB left this tab holding a dead string with no way to find out.
//   3. Any non-OK response was read as "session is dead" → forced re-login,
//      even when the winner of the race had written a perfectly good token to
//      localStorage microseconds earlier.
// The three guards below answer those one for one. A refresh may now fail ONLY
// because the grant is genuinely revoked or expired — never because we raced
// ourselves.

// The newest refresh token this browser knows about. localStorage wins over the
// in-memory copy: another tab may have rotated it since we cached ours.
function newestRefreshToken() {
  return lsGet("cloverag_refresh_token") || _supabaseRefreshToken || null;
}

// One refresh POST. Resolves { ok } or { ok:false, alreadyUsed } — the caller
// needs that distinction, because "already used" is recoverable and everything
// else is not.
async function postRefresh(token) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
    method: "POST",
    headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: token }),
  });
  if (res.ok) {
    adoptSession(await res.json());
    return { ok: true };
  }
  let body = {};
  try { body = await res.json(); } catch (e) { body = {}; }
  const code = String(body.error_code || body.error || "");
  return { ok: false, status: res.status, code, alreadyUsed: code === "refresh_token_already_used" };
}

async function supabaseRefreshSession() {
  // Single-flight: concurrent callers share one network call and one rotation.
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    const token = newestRefreshToken();
    if (!token) return false;
    if (token === _deadRefreshToken) return false;   // already proven dead — don't ask twice

    let r;
    try { r = await postRefresh(token); }
    catch (e) {
      // Network blip / offline. NOT a dead session — keep what we have and let
      // the caller fall back to the cached access token.
      noteAuthEvent("refresh-network-error", String(e && e.message ? e.message : e));
      return false;
    }
    if (r.ok) return true;

    if (r.alreadyUsed) {
      // We lost a rotation race. Whoever won wrote a fresh pair to localStorage;
      // adopt it instead of logging the user out. This is the exact path that
      // used to end in a password prompt.
      const newer = lsGet("cloverag_refresh_token");
      if (newer && newer !== token) {
        try {
          const r2 = await postRefresh(newer);
          if (r2.ok) return true;
        } catch (e) { return false; }
      }
      // No newer refresh token — but the winner may still have left a valid
      // access token behind, which is enough to stay signed in until it lapses.
      const at = lsGet("cloverag_access_token");
      if (at && !isJwtExpired(at)) { _supabaseAccessToken = at; return true; }
      _deadRefreshToken = token;
      noteAuthEvent("refresh-token-already-used", "lost a rotation race with no usable token left");
      return false;
    }

    _deadRefreshToken = token;
    noteAuthEvent("refresh-rejected", `status=${r.status} code=${r.code || "?"}`);
    return false;
  })().finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
}

function scheduleTokenRefresh(expiresInSec) {
  if (_refreshTimer) clearTimeout(_refreshTimer);
  // Refresh 5 minutes before expiry
  const ms = Math.max((expiresInSec - 300) * 1000, 30000);
  _refreshTimer = setTimeout(() => supabaseRefreshSession(), ms);
}

// Decode a JWT exp claim. Returns true if the token is missing, malformed,
// or expired (with a 60-second safety margin so we don't race the server).
function isJwtExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload?.exp) return true;
    return payload.exp * 1000 < Date.now() + 60000;
  } catch {
    return true;
  }
}

// ── WHAT AN EMPTY BOOK READ MEANS ───────────────────────────────────────────
// `farmers` coming back with no rows has three causes and only one of them is a
// fact about the agency. MEASURED against the live project 2026-10-11:
//   • a request carrying only the anon `apikey` and NO Authorization header —
//     exactly what getAuthHeaders() sends once the session is gone — returns
//     **200 with ZERO ROWS**, not the 401 the comment above that function
//     claims;
//   • a request carrying an unparseable or expired bearer returns 401
//     (PGRST301), which loadData's own `.catch(() => [])` flattens into the
//     same zero rows;
//   • and Kevin's own account, asked properly, returns all 283.
// So the row count cannot tell a dead session from an empty agency, and the app
// must not guess. It says which of the three it is, or says it does not know.
function bookReadFailureMessage(err) {
  if (err) return `The book could not be read: ${err.message || err}. This is a failed request, not an empty book — nothing on this screen is your customer list. Reload, or sign in again if your session has expired.`;
  const tok = typeof window !== "undefined" ? lsGet("cloverag_access_token") : null;
  if (!tok) return "You are not signed in, so the book came back empty. Nothing on this screen is your customer list — sign in again.";
  if (isJwtExpired(tok)) return "Your session has expired, so the book came back empty. Nothing on this screen is your customer list — sign in again.";
  return "The book read succeeded and returned no growers. The session is valid, so this is not a sign-in problem — reload, and if it persists the read is being refused somewhere between here and the database.";
}

// Restore the persisted session at app boot. We MUST await the refresh —
// the timer-based refresh in scheduleTokenRefresh() doesn't survive closed
// tabs or a sleeping laptop, so the access_token in localStorage is often
// expired by the time the user comes back. If we don't await the refresh,
// Dashboard's initial data fetches fire with a dead JWT and the page loads
// empty until the user manually refreshes (which incidentally lets the
// background refresh from the prior load finish first).
async function restoreSession() {
  const at = lsGet("cloverag_access_token");
  const rt = lsGet("cloverag_refresh_token");
  if (!at || !rt) return false;
  _supabaseAccessToken = at;
  _supabaseRefreshToken = rt;
  if (await supabaseRefreshSession()) return true;

  // The refresh failed. Before forcing a re-login — the thing a farmer
  // experiences as "it logged me out again" — look at localStorage as it stands
  // NOW, not as we read it above. restoreSession is called from two places on a
  // cold boot (App's effect, then LoginScreen's), and other tabs rotate the same
  // session, so a valid token may have landed while we were in flight.
  const latest = lsGet("cloverag_access_token") || at;
  if (!isJwtExpired(latest)) { _supabaseAccessToken = latest; return true; }
  noteAuthEvent("session-restore-failed", "refresh rejected and the cached access token is expired");
  return false;
}

// Adopt a session obtained outside the password login flow — specifically the
// tokens GoTrue puts in the URL hash after an invite / recovery magic link.
function setSupabaseTokens(access, refresh, expiresInSec) {
  adoptSession({ access_token: access, refresh_token: refresh, expires_in: expiresInSec });
}

function supabaseSignOut() {
  _supabaseAccessToken = null;
  _supabaseRefreshToken = null;
  _refreshInFlight = null;
  if (_refreshTimer) clearTimeout(_refreshTimer);
  try {
    localStorage.removeItem("cloverag_access_token");
    localStorage.removeItem("cloverag_refresh_token");
    localStorage.removeItem("cloverag_auth");
  } catch (e) { /* storage unavailable — the in-memory clear above is what matters */ }
}

// ── Cross-tab token sync ────────────────────────────────────────────────────
// `storage` fires in every OTHER tab of this origin. Without it, a tab that sat
// open while another rotated the token kept using a dead string in memory and
// hard-failed its next refresh. This is the cheap half of the fix; the retry
// inside supabaseRefreshSession is the half that covers tabs which were mid-
// flight when the rotation landed.
if (typeof window !== "undefined" && !window.__silasAuthSyncInstalled) {
  window.__silasAuthSyncInstalled = true;
  window.addEventListener("storage", (e) => {
    if (!e || !e.key) return;
    if (e.key === "cloverag_access_token"  && e.newValue) _supabaseAccessToken  = e.newValue;
    if (e.key === "cloverag_refresh_token" && e.newValue) _supabaseRefreshToken = e.newValue;
  });

  // Refresh on resume. scheduleTokenRefresh's timer does NOT survive a phone
  // locking or an iOS home-screen app being backgrounded — the OS suspends it,
  // so a farmer reopening the installed app after a few hours comes back with a
  // dead JWT and every first fetch has to 401 before it can recover. Topping the
  // token up when the app becomes visible again makes the reopen just work.
  // Single-flight means this can't collide with a refresh already running.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (!lsGet("cloverag_refresh_token")) return;               // signed out — nothing to do
    if (!isJwtExpired(lsGet("cloverag_access_token"))) return;  // still good
    supabaseRefreshSession();
  });
}

// ── Auth breadcrumbs ────────────────────────────────────────────────────────
// Auth failures are exactly the errors reportClientError CANNOT capture: it
// requires a user id (RLS on client_errors is user_id = auth.uid()), and a user
// who just got bounced to the login screen doesn't have one. That blind spot is
// why the Howlett incident left nothing in client_errors and had to be dug out
// of GoTrue logs that roll off after 24 hours.
// So: park the breadcrumb in localStorage, and flush it as a normal client_errors
// row on the next successful sign-in — authenticated, so RLS is satisfied, and
// attributed to the user it actually happened to.
const AUTH_BREADCRUMB_KEY = "cloverag_auth_events";
const MAX_AUTH_BREADCRUMBS = 5;

function noteAuthEvent(kind, detail) {
  try {
    const prior = JSON.parse(lsGet(AUTH_BREADCRUMB_KEY) || "[]");
    const list = Array.isArray(prior) ? prior : [];
    list.push({ kind, detail: String(detail || "").slice(0, 300), at: new Date().toISOString() });
    lsSet(AUTH_BREADCRUMB_KEY, JSON.stringify(list.slice(-MAX_AUTH_BREADCRUMBS)));
  } catch (e) { /* never let telemetry break auth */ }
}

function flushAuthBreadcrumbs() {
  try {
    const raw = lsGet(AUTH_BREADCRUMB_KEY);
    if (!raw) return;
    try { localStorage.removeItem(AUTH_BREADCRUMB_KEY); } catch (e) {}
    const list = JSON.parse(raw);
    if (!Array.isArray(list) || list.length === 0) return;
    const summary = list.map((e) => `${e.at} ${e.kind}: ${e.detail}`).join("\n");
    // Deferred: the JWT from this sign-in has to be in place before the insert.
    setTimeout(() => {
      reportClientError("auth", new Error(`Signed in after ${list.length} auth failure(s) — ${list[list.length - 1].kind}`), { authTrail: summary });
    }, 1500);
  } catch (e) { /* never let telemetry break login */ }
}

// Supabase REST fetch that self-heals an expired session: on a 401 (JWT expired),
// refresh the token once and retry. Without this, a token that lapsed while the tab
// sat idle overnight surfaces as "Supabase error: 401" on the next fresh read (e.g.
// the Field Mapper's policy_fields load) instead of transparently recovering. Mirrors
// adminApiFetch's behavior for the /api/* path.
// Read a response as JSON and, when it ISN'T JSON, say what actually happened.
//
// A timed-out or crashed function answers with Vercel's PLAIN-TEXT page — "A
// server error has occurred", "An error occurred with this application" — so a
// bare r.json() throws "Unexpected token 'A' … is not valid JSON". That names
// neither the endpoint nor the cause and reads like corrupt data rather than a
// 500 or a timeout, which is how a Build-maps failure came back as a complaint
// about JSON syntax.
//
// Module scope on purpose: the same shape exists at every direct fetch site in
// this file, not just the John Deere panel where it started.
async function asJson(r) {
  const txt = await r.text();
  try { return JSON.parse(txt); }
  catch (e) {
    // Classify off the STATUS, not the prose. Vercel shows the same "An error
    // occurred with this application" page for a timeout and for a crash, so the
    // wording can't tell them apart — the code can.
    if (r.status === 504 || r.status === 408 || /FUNCTION_INVOCATION_TIMEOUT/i.test(txt))
      throw new Error(`The server took too long to answer (HTTP ${r.status}). Anything already saved was kept — run it again and it picks up where it left off.`);
    if (r.status >= 500)
      throw new Error(`The server hit an error (HTTP ${r.status}). Nothing was lost — try again, and if it repeats it needs looking at.`);
    throw new Error(`HTTP ${r.status}: ${txt.slice(0, 160) || "empty response"}`);
  }
}

async function authedFetch(url, init = {}) {
  // Any non-GET goes through here — sbUpsert/sbInsert/sbDelete AND the ~17 direct
  // PATCH call sites scattered through the views. Invalidating the read cache here
  // rather than in each helper means a new write path can't silently forget to do
  // it and leave a screen showing the pre-write value.
  const method = (init.method || "GET").toUpperCase();
  const isWrite = method !== "GET" && method !== "HEAD";

  const withAuth = () => ({ ...init, headers: { ...getAuthHeaders(), ...(init.headers || {}) } });
  let res = await fetch(url, withAuth());
  if (res.status === 401) {
    const ok = await supabaseRefreshSession();
    if (ok) res = await fetch(url, withAuth());
  }
  // Cleared AFTER the response lands, so a read racing the write can't re-cache
  // the pre-write value in between.
  if (isWrite) sbCacheClear();
  return res;
}

// Supabase caps a REST response at 1000 rows, so anything bigger is paginated by
// Range header. Pages are fetched SB_CONCURRENCY at a time rather than one after
// another — the serial version turned the MI Yield season scan (56k rows) into 57
// round-trips back to back, which is where most of that page's load time went.
const SB_PAGE_SIZE   = 1000;
const SB_CONCURRENCY = 6;

// ── sbFetch cache + in-flight dedupe ─────────────────────────────────────────
// Two separate problems, both measured 2026-07-29:
//   1. The screen router is a ternary chain, so changing nav UNMOUNTS the old view
//      and its useEffect fetches re-fire from scratch on return. A short TTL makes
//      back-and-forth navigation instant while never serving data older than
//      SB_CACHE_MS.
//   2. Sibling components fire the SAME query in the same tick — field_geometries
//      is requested from 7 call sites, nass_data from 12. In-flight dedupe collapses
//      those onto one request. Always safe: they'd resolve to identical data anyway.
//
// Safety notes, since a read cache is exactly the sort of thing that causes a
// "why is it showing the old number" bug:
//   • ANY write (sbUpsert / sbInsert / sbDelete) clears the cache outright, so
//     read-after-write inside an import or an inline edit always sees the new row.
//   • Pass { fresh: true } to bypass for a specific call.
//   • Cache lives in module scope and dies with the page. Sign-out already does a
//     full window.location.reload(), and admin "preview as" is a client-side filter
//     that never swaps the JWT — so a cached row can't outlive the session that was
//     entitled to it.
const SB_CACHE_MS = 30_000;
const _sbCache    = new Map();   // url -> { at, rows }
const _sbInflight = new Map();   // url -> Promise<rows>
function sbCacheClear() { _sbCache.clear(); }

async function _sbFetchPage(baseUrl, page) {
  const from = page * SB_PAGE_SIZE;
  const res = await authedFetch(baseUrl, {
    headers: {
      "Range-Unit": "items",
      "Range": `${from}-${from + SB_PAGE_SIZE - 1}`,
      "Prefer": "count=none",
    },
  });
  // 416 = the range starts past the last row. Only reachable because a parallel
  // batch can over-request past the end; it means "no more rows", not an error.
  if (res.status === 416) return [];
  if (!res.ok) throw new Error(`Supabase error: ${res.status}`);
  return res.json();
}

async function _sbFetchAllPages(table, baseUrl, filters, order, maxPages) {
  const all = [];
  let page = 0;
  let reachedEnd = false;

  // A query that carries its OWN limit cannot span pages, so never speculate past
  // the first one. Pages are fetched SB_CONCURRENCY at a time in parallel, so a
  // `limit=1` freshness lookup fired page 0 plus five more at Range: 1000-1999 and
  // beyond — each of which PostgREST answers 416 Range Not Satisfiable against a
  // one-row result. The rows returned were always correct (the loop stops at the
  // first short page and discards the rest), but every such call made five doomed
  // requests and printed five console errors. Market Intelligence alone threw ten.
  // That is what a real failure has to compete with in the console, which is the
  // actual cost — the browser sweep found this by looking for JS errors and had to
  // wade through these first.
  const own = /[?&]limit=(\d+)(&|$)/.exec(baseUrl);
  if (own && Number(own[1]) <= SB_PAGE_SIZE) {
    return await _sbFetchPage(baseUrl, 0);
  }

  while (!reachedEnd && page < maxPages) {
    const batch = [];
    for (let i = 0; i < SB_CONCURRENCY && page + i < maxPages; i++) batch.push(page + i);
    const chunks = await Promise.all(batch.map(p => _sbFetchPage(baseUrl, p)));
    // Consume IN ORDER and stop at the first short page, discarding anything the
    // batch already fetched beyond it. That reproduces the old serial semantics
    // exactly — parallelism changes the timing, never the returned rows.
    for (const chunk of chunks) {
      for (const row of chunk) all.push(row);
      if (chunk.length < SB_PAGE_SIZE) { reachedEnd = true; break; }
    }
    page += batch.length;
  }

  // Loud guard against SILENT truncation — the class of bug that froze the grid
  // weather at May 28. If we exhausted every page and never saw a short one, there
  // is more data on the server that we dropped. An ascending fetch drops the NEWEST
  // rows here, which reads as "frozen" data downstream. Warn so it can never hide
  // again; the fix is a higher maxPages, tighter filters, or order=...desc.
  if (!reachedEnd) {
    console.warn(`[sbFetch] "${table}" hit the ${maxPages}-page cap (${all.length} rows) — MORE rows exist and were dropped. If ordered ascending, the newest rows are missing (stale-data risk). Raise maxPages, tighten filters, or order descending. filters=[${filters.join(", ")}] order=${order || "none"}`);
  }
  return all;
}

async function sbFetch(table, options = {}) {
  // safety cap on total rows (default 50k). Override via `maxPages` for tables
  // whose per-year row count outgrows 50k mid-season (e.g. the 338-cell grid
  // weather daily, which crosses 50k rows around late May). Ascending fetches
  // silently drop the NEWEST rows when they hit this cap, so any all-cell
  // full-season scan must raise it. See the grid-weather fetch below.
  // `limit` IS HONOURED. It was accepted and silently dropped until 2026-08-13 —
  // not in this destructure, not in the URL — so a caller asking for one row got
  // the whole table and no error. The Field Mapper's "what is the newest crop
  // year" probe passed `limit: 1` and pulled every crop_year in policy_fields:
  // 28,755 rows over 29 paged round trips to read a single number, growing with
  // every year the M13 backfill writes. It returned the RIGHT answer, which is
  // exactly why it went unnoticed.
  //
  // Passing it through the URL also arms the existing single-page short-circuit
  // in _sbFetchAllPages (it reads `limit=` off baseUrl), so a limited query stops
  // speculating past its own result and stops printing 416s.
  const { select = "*", filters = [], order = null, limit = null, maxPages = 50, fresh = false } = options;
  const baseUrl = `${SUPABASE_URL}/rest/v1/${table}?select=${select}` +
    filters.map(f => `&${f}`).join("") +
    (order ? `&order=${order}` : "") +
    (limit != null ? `&limit=${limit}` : "");

  if (!fresh) {
    const hit = _sbCache.get(baseUrl);
    if (hit && Date.now() - hit.at < SB_CACHE_MS) return hit.rows;
    const pending = _sbInflight.get(baseUrl);
    if (pending) return pending;
  }

  const run = _sbFetchAllPages(table, baseUrl, filters, order, maxPages)
    .then(rows => { _sbCache.set(baseUrl, { at: Date.now(), rows }); return rows; })
    .finally(() => { _sbInflight.delete(baseUrl); });
  _sbInflight.set(baseUrl, run);
  return run;
}

// HOW MANY, without downloading any of them. PostgREST puts the total in
// Content-Range when asked for an exact count over a zero-width range, and
// Supabase exposes that header to the browser (measured, not assumed —
// access-control-expose-headers lists Content-Range). Same idiom as
// _fieldGeomFingerprint below.
//
// This exists for questions of the form "does this customer have ANY", where the
// table is tens of thousands of rows and the answer is one number: counting
// policy_fields for one grower by fetching them would pull 15,733 CY2026 rows to
// learn "97".
//
// Returns null — never 0 — when the count can't be read. A failed probe that
// answers "none" would assert the very data gap it was asked to check.
async function sbCount(table, filters = [], select = "id") {
  try {
    const url = `${SUPABASE_URL}/rest/v1/${table}?select=${select}` + filters.map(f => `&${f}`).join("") + "&limit=1";
    const res = await authedFetch(url, { headers: { "Range-Unit": "items", "Range": "0-0", Prefer: "count=exact" } });
    if (!res.ok) return null;
    const n = Number((res.headers.get("content-range") || "").split("/")[1]);
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}

async function sbUpsert(table, rows, onConflict) {
  if (!rows || rows.length === 0) return;
  const url = onConflict
    ? `${SUPABASE_URL}/rest/v1/${table}?on_conflict=${encodeURIComponent(onConflict)}`
    : `${SUPABASE_URL}/rest/v1/${table}`;
  const res = await authedFetch(url, {
    method: "POST",
    headers: {
      "Prefer": "resolution=merge-duplicates,return=representation",
    },
    body: JSON.stringify(rows),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Supabase upsert ${table}: ${err}`);
  }
  return res.json();
}

async function sbDelete(table, filter) {
  const res = await authedFetch(`${SUPABASE_URL}/rest/v1/${table}?${filter}`, {
    method: "DELETE",
    headers: {
      "Prefer": "return=minimal",
    },
  });
  if (!res.ok) throw new Error(`Supabase delete ${table}: ${res.status}`);
}

// `returning:"minimal"` exists because `return=representation` makes PostgREST
// SELECT the row back after inserting it, which needs a SELECT policy the writer
// may not have. A table that is write-only by design (INSERT policy, no SELECT —
// client_errors is exactly that) therefore FAILS the whole insert on the read-back,
// not on the write. That silently ate every portal crash report ever filed: the
// row passed WITH CHECK, the read-back returned nothing, sbInsert threw, and
// reportClientError's catch swallowed it. Use minimal for fire-and-forget writes.
async function sbInsert(table, rows, opts = {}) {
  if (!rows || rows.length === 0) return;
  const minimal = opts.returning === "minimal";
  const res = await authedFetch(`${SUPABASE_URL}/rest/v1/${table}`, {
    method: "POST",
    headers: {
      "Prefer": minimal ? "return=minimal" : "return=representation",
    },
    body: JSON.stringify(rows),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Supabase insert ${table}: ${err}`);
  }
  return minimal ? null : res.json();   // return=minimal sends an empty body
}

// ─────────────────────────────────────────────────────────────────────────────
// CLIENT ERROR REPORTING
// ─────────────────────────────────────────────────────────────────────────────
// Crashes on a farmer's phone used to reach their console and nowhere else. These
// push them to `client_errors` (admin-read only) so we find out before they call.
//
// Rules this thing must never break:
//   1. It can NEVER throw. A failing error-reporter that breaks the app is worse
//      than no reporter, so every path is swallowed.
//   2. It never reports for a signed-out user — RLS requires user_id = auth.uid(),
//      so an anon insert would 401 in a loop on the login screen.
//   3. It dedupes per session. A crash inside a render loop can fire hundreds of
//      times a second; we'd rather have one row than a self-inflicted write storm.

const _reportedErrors = new Set();   // fingerprints already sent this session
let _errorReportCount = 0;
const MAX_ERROR_REPORTS_PER_SESSION = 20;

// Coarse group key so repeats of one bug collapse into one row per session and
// can be counted together in the admin list.
function errorFingerprint(kind, message, stack) {
  const firstFrame = String(stack || "").split("\n").find(l => /\.js/.test(l)) || "";
  return [kind || "?", String(message || "").slice(0, 120), firstFrame.trim().slice(0, 120)].join("|");
}

async function reportClientError(kind, error, extra = {}) {
  try {
    const userId = getCurrentUserId();
    if (!userId) return;                                    // rule 2
    if (_errorReportCount >= MAX_ERROR_REPORTS_PER_SESSION) return;

    const message = String(error?.message || error || "Unknown error").slice(0, 2000);
    const stack   = error?.stack ? String(error.stack).slice(0, 6000) : null;
    const fp      = errorFingerprint(kind, message, stack);
    if (_reportedErrors.has(fp)) return;                    // rule 3
    _reportedErrors.add(fp);
    _errorReportCount++;

    await sbInsert("client_errors", [{
      user_id:         userId,
      email:           getCurrentUserEmail() || null,
      surface:         isCurrentUserAdmin() ? "agent" : "portal",
      kind,
      message,
      fingerprint:     fp,
      stack,
      // authTrail rides in the same column as the component stack: both answer
      // "what led here", and neither ever appears on the same row as the other.
      component_stack: extra.componentStack ? String(extra.componentStack).slice(0, 6000)
                     : extra.authTrail     ? String(extra.authTrail).slice(0, 6000)
                     : null,
      url:             typeof window !== "undefined" ? String(window.location.href).slice(0, 500) : null,
      user_agent:      typeof navigator !== "undefined" ? String(navigator.userAgent).slice(0, 500) : null,
      // ⚠ A PROBE'S CRASH IS NOT A CUSTOMER'S, and nothing on this row could tell them
      // apart. tools/check-screens.js mints a session for an allowlisted ADMIN — the same
      // address Kevin signs in with — and can run against PRODUCTION, so email, url and
      // app_version are all identical to a real session. The register calls this the
      // prerequisite for alerting on this table at all.
      //
      // Carried in app_version rather than as a new column: it is a fact about WHICH BUILD
      // AND WHICH CLIENT produced the row, which is exactly what that column already
      // answers, and a schema change is not needed to stop an alert crying wolf. Reading
      // it must never throw — storage is unavailable in a private window — and a failure
      // to read it degrades to the ordinary stamp, which is the safe direction: a probe
      // row mistaken for a real one is noise, the reverse would hide a customer's crash.
      app_version:     (() => {
        try {
          const probe = window.localStorage.getItem("cloverag_probe");
          return probe ? "probe:" + String(probe).slice(0, 40) + " " + APP_VERSION : APP_VERSION;
        } catch { return APP_VERSION; }
      })(),
    }], { returning: "minimal" });
  } catch (e) {
    // rule 1 — never let the reporter surface an error of its own.
  }
}

// Catch what React's ErrorBoundary can't see: throws in event handlers, async
// callbacks, and rejected promises. Installed once at module load.
if (typeof window !== "undefined" && !window.__silasErrorHooksInstalled) {
  window.__silasErrorHooksInstalled = true;
  window.addEventListener("error", (e) => {
    reportClientError("unhandled", e?.error || new Error(e?.message || "window.onerror"));
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e?.reason;
    reportClientError("rejection", r instanceof Error ? r : new Error(String(r)));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// SUPABASE STORAGE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
// Upload with a REAL progress figure. `sbStorageUpload` above uses fetch(), which reports
// nothing until it is finished — fine for a signed PDF, wrong for a yield map, where a
// 40 MB shapefile looks like a frozen screen for half a minute.
//
// ⚠ A SEPARATE FUNCTION, NOT A CHANGED ONE. Four callers depend on sbStorageUpload and none
// of them wants XHR; widening it would make every one of them carry a parameter they ignore.
//
// ⚠ AND IT REPORTS ONLY WHAT IT ACTUALLY KNOWS. `lengthComputable` is false for some bodies,
// and a bar that invents a percentage to look busy is a worse lie than a bar that says it
// cannot tell — so the callback gets null and the UI shows an indeterminate state.
async function sbStorageUploadProgress(bucket, path, file, onProgress) {
  const token = _supabaseAccessToken || SUPABASE_KEY;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`);
    xhr.setRequestHeader("apikey", SUPABASE_KEY);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    // ⚠ WINDOWS CALLS A ZIP `application/x-zip-compressed`, macOS and Linux call it
    // `application/zip`, and some browsers send nothing at all. The bucket checks the type it
    // is given, so passing `file.type` straight through made the upload succeed or fail BY
    // OPERATING SYSTEM — Kevin hit a 415 InvalidMimeType on the first real file. Normalised
    // by EXTENSION, which is the thing that actually says what the file is.
    const ext = String(path).toLowerCase().split(".").pop();
    const ctype = ext === "zip" ? "application/zip" : (file.type || "application/octet-stream");
    xhr.setRequestHeader("Content-Type", ctype);
    xhr.setRequestHeader("x-upsert", "true");
    xhr.upload.onprogress = (e) => {
      if (!onProgress) return;
      onProgress(e.lengthComputable && e.total ? Math.round((e.loaded / e.total) * 100) : null,
                 e.loaded || 0, e.total || file.size || 0);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve({ path })
      : reject(new Error(`Storage upload failed (${xhr.status}): ${String(xhr.responseText || "").slice(0, 200)}`)));
    xhr.onerror = () => reject(new Error("Storage upload failed — the connection dropped"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.send(file);
  });
}
async function sbStorageUpload(bucket, path, file) {
  const token = _supabaseAccessToken || SUPABASE_KEY;
  const res = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type || "application/pdf",
      "x-upsert": "true",
    },
    body: file,
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Storage upload failed: ${err}`);
  }
  return res.json();
}

async function sbStorageSignedUrl(bucket, path, expiresIn = 3600) {
  const token = _supabaseAccessToken || SUPABASE_KEY;
  const res = await fetch(`${SUPABASE_URL}/storage/v1/object/sign/${bucket}/${path}`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ expiresIn }),
  });
  if (!res.ok) throw new Error(`Signed URL failed: ${res.status}`);
  const data = await res.json();
  return `${SUPABASE_URL}/storage/v1${data.signedURL}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// FARMER NAME MATCHING
// Mirrors normFarmerName in api/m13-sync.js + api/nau-sync.js — keep the three
// in step. Collapses the variation classes that were minting duplicate
// customers (case, punctuation, word order, entity suffix, single-letter middle
// initials) and nothing else. JR/SR/II/III are KEPT: a father and son insured on
// the same ground are two insureds, and dropping the suffix would merge them.
// ─────────────────────────────────────────────────────────────────────────────
const FARMER_NAME_NOISE = new Set(["LLC","INC","CORP","CORPORATION","CO","LTD","LP","LLP","DBA","THE","AND"]);
// A trade name in parentheses is the SAME insured under a DBA, not a second
// customer: "BENTLEY, ERIC D (OAK HILL FARMS)" and "ERIC BENTLEY" are one man.
// Whether the AIP file carries the DBA varies by year — the CY2027 rollover
// dropped it and minted four duplicate farmers overnight (Bentley, Darling,
// Moran, Shaffer), each reading as new business AND lost business at once.
// Stripping it is safe because it only ever REMOVES words: it cannot join two
// people who don't already share every remaining word. Verified against the
// live book — the only keys it collapses are those four pairs.
const stripDba = (s) => String(s || "").replace(/\([^)]*\)/g, " ");
function normFarmerName(name) {
  return stripDba(name).toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim().split(" ")
    .filter(w => w.length > 1 && !FARMER_NAME_NOISE.has(w))
    .sort().join(" ");
}

// Return the name an existing farmer is ALREADY stored under, when `name` is a
// spelling of that same customer. Returns null when there's no match, and also
// when two farmers answer to the same normalized name — picking one would file
// the import against the wrong customer, so the caller falls back to the exact
// name and the duplicate stays visible instead of being silently chosen.
// Every spelling a farmer row answers to — the stored name plus any recorded in
// metadata.name_aliases, for the variations no rule can derive (NATHANIEL vs
// NATHANAEL). Kept in step with the copies in m13-sync.js / nau-sync.js.
function farmerNameKeys(f) {
  const raw = [f && f.name, ...(((f && f.metadata && f.metadata.name_aliases) || []))];
  return [...new Set(raw.map(normFarmerName).filter(Boolean))];
}

async function resolveExistingFarmerName(name) {
  const key = normFarmerName(name);
  if (!key) return null;
  try {
    const all = await sbFetch("farmers", { select: "id,name,metadata" });
    const hits = (all || []).filter(f => farmerNameKeys(f).includes(key));
    if (hits.length !== 1) return null;
    return hits[0].name;
  } catch { return null; }   // never block a save on the lookup
}

// ─────────────────────────────────────────────────────────────────────────────
// SAVE FARMER TO SUPABASE
// FIX: Policy row now uses upsert on (farmer_id, crop_year, policy_number)
// unique constraint so re-importing the same farm always resolves to the
// same policy_id — keeping crop_coverage rows attached correctly on reload.
// ─────────────────────────────────────────────────────────────────────────────
async function saveFarmerToSupabase(farmer) {
  const steps = [];
  const log = (step, ok, detail) => steps.push({ step, ok, detail });

  // ═══════════════════════════════════════════════════════════════════════════
  // M13 OWNERSHIP GUARD
  // M13 sync is the source of truth for crop_coverage, pp_eligibility,
  // drp_endorsements, lrp_endorsements, and prf data for R&H (MP-) and NAU
  // (state-format) policies. saveFarmerToSupabase should NOT write child data
  // for these policies — it would create phantom data that conflicts with M13.
  // Only allow child data writes for non-M13 policies (other AIPs).
  // ═══════════════════════════════════════════════════════════════════════════
  const pn = farmer.policyNumber || '';
  // RARE EXCEPTION: a few policies carry an MP-/NAU-style number but are written
  // by a DIFFERENT agent, so they never appear in our M13/NAU SFTP feed. The
  // sync therefore owns nothing for them and the guard must NOT block manual PDF
  // upload of their coverage/APH. Normalized match (uppercase, strip non-alnum)
  // survives leading-zero variants (MP-0936636 vs MP-936636). Add new ones here.
  //
  // An entry is a claim about the FILE, not the customer, and it EXPIRES the day
  // the AIP starts sending the policy: the sync then inserts its rows without
  // deleting the manual ones and everything reads twice. Howlett Farms sat here
  // until 2026-08-11, when R&H began shipping MP-0936636 / MP-0927356 and the
  // CY2026 card doubled to $3.84M against $1.92M real. Mirror of the set in
  // api/m13-sync.js — the two must stay in step.
  const EXTERNAL_MANAGED_POLICIES = new Set([
    // (empty) — see api/m13-sync.js for the Howlett removal note.
  ]);
  const _normPN = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const matchesSyncFormat = isSyncFormatPolicyNumber(pn);
  const isM13Owned = matchesSyncFormat && !EXTERNAL_MANAGED_POLICIES.has(_normPN(pn));
  if (isM13Owned) {
    log("m13_guard", true, `Policy ${pn} is M13-owned — skipping child data writes (farmer row only)`);
  } else if (matchesSyncFormat) {
    log("m13_guard", true, `Policy ${pn} matches sync format but is externally managed — allowing manual child writes`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STRICT DATA SILO GUARDRAILS
  // Each coverage type ONLY touches its own tables. Importing PRF cannot
  // delete or modify crop_coverage, pp_eligibility, drp_endorsements, or
  // lrp_endorsements — and vice versa. This prevents cross-contamination.
  //
  // Silos:
  //   SPRING CROP  → crop_coverage (non-WHEAT), pp_eligibility
  //   FALL CROP    → crop_coverage (WHEAT only)
  //   DRP          → drp_endorsements
  //   LRP          → lrp_endorsements
  //   PRF          → prf_policies, prf_intervals
  // ═══════════════════════════════════════════════════════════════════════════
  
  const hasSpringCropData = farmer.approvedYields?.some(y => !FALL_CROPS.includes((y.crop||'').toUpperCase()))
                         || farmer.ppEligibility?.some(p => !FALL_CROPS.includes((p.crop||'').toUpperCase()));
  const hasFallCropData   = farmer.approvedYields?.some(y =>  FALL_CROPS.includes((y.crop||'').toUpperCase()));
  const hasScoEcoData     = farmer.scoEcoEndorsements?.length > 0;
  const hasDRPData        = farmer.drpPolicies?.some(p => p.endorsements?.length > 0);
  const hasLRPData        = farmer.lrpPolicies?.some(p => p.endorsements?.length > 0);
  const hasPRFData        = farmer.prfPolicies?.length > 0;

  // Determine what this save is primarily about (for logging)
  const siloFlags = [];
  if (hasSpringCropData) siloFlags.push('SPRING_CROP');
  if (hasFallCropData)   siloFlags.push('FALL_CROP');
  if (hasScoEcoData)     siloFlags.push('SCO_ECO');
  if (hasDRPData)        siloFlags.push('DRP');
  if (hasLRPData)        siloFlags.push('LRP');
  if (hasPRFData)        siloFlags.push('PRF');
  log("silo_check", true, `Active silos: [${siloFlags.join(', ')}]`);

  // 1. Upsert farmer row.
  // The upsert conflict key is `name`, an EXACT string match — so an import that
  // spells an existing customer differently ("ERNEST L GATES & SONS LLC" vs
  // "ERNEST GATES & SONS LLC", "JANIGA, LEONARD" vs "LEONARD JANIGA", or just a
  // different case) inserts a SECOND farmer and splits that customer's policies
  // between the two rows. Eight customers in the live book were duplicated this
  // way. Resolve to the stored spelling first so the upsert lands on the
  // existing row; the import never renames the customer.
  const resolvedName = await resolveExistingFarmerName(farmer.name);
  if (resolvedName && resolvedName !== farmer.name) {
    log("farmer name match", true, `"${farmer.name}" → existing "${resolvedName}"`);
  }
  const farmerRows = await sbUpsert("farmers", [{
    name:        resolvedName || farmer.name,
    entity_type: farmer.entityType || null,
    agent_code:  farmer.agentCode  || null,
    address:     farmer.address    || null,
    phone:       farmer.phone      || null,
  }], "name");

  let farmerId;
  if (farmerRows?.[0]?.id) {
    farmerId = farmerRows[0].id;
    log("farmer upsert", true, `id=${farmerId.slice(0,8)}`);
  } else {
    const existing = await sbFetch("farmers", { filters: [`name=eq.${encodeURIComponent(resolvedName || farmer.name)}`] });
    if (!existing?.length) throw new Error(`Could not save or find farmer: ${farmer.name}`);
    farmerId = existing[0].id;
    log("farmer fetch fallback", true, `id=${farmerId.slice(0,8)}`);
  }

  // 2. Upsert policy row — stable on (farmer_id, crop_year, policy_number)
  const policyNumber = farmer.policyNumber || "";
  const cropYear     = farmer.cropYear || CURRENT_CROP_YEAR;

  // Build the upsert payload. agent_name and agency_name are OMITTED when
  // the manual import doesn't have a real value — otherwise PostgREST upsert
  // would clobber the M13/NAU-derived agent of record with null. The M13 +
  // NAU syncs are the source of truth for those fields; manual PDF imports
  // can only overwrite when they have a non-empty value.
  const policyPayload = {
    farmer_id:     farmerId,
    policy_number: policyNumber,
    crop_year:     cropYear,
    policy_type:   farmer.policyTypes?.[0] || "crop",
    county:        normalizeCounty(farmer.county) || null,
    state:         farmer.state      || "NY",
    insurer:       farmer.insurer    || null,
  };
  // "Multiple" is the loader's sentinel for a farm holding paper from two
  // carriers — it is not an AIP and must never be persisted. Omit the field
  // entirely in that case so the upsert leaves the stored per-policy carrier
  // alone, the same way agent_name is protected just below. A farmer object
  // built from an extraction still carries a real AIP and writes normally.
  if (farmer.aip && farmer.aip !== "Multiple") policyPayload.aip = farmer.aip;
  else if (!farmer.aip) policyPayload.aip = "NAU";
  if (farmer.agent && String(farmer.agent).trim()) policyPayload.agent_name = farmer.agent;
  if (farmer.agency && String(farmer.agency).trim()) policyPayload.agency_name = farmer.agency;

  let policyId;
  const upserted = await sbUpsert("policies", [policyPayload], "farmer_id,crop_year,policy_number");

  if (upserted?.[0]?.id) {
    policyId = upserted[0].id;
    log("policy upsert", true, `id=${policyId.slice(0,8)} policyNum=${policyNumber}`);
  } else {
    const found = await sbFetch("policies", {
      filters: [
        `farmer_id=eq.${farmerId}`,
        `crop_year=eq.${cropYear}`,
        `policy_number=eq.${encodeURIComponent(policyNumber)}`,
      ]
    });
    if (!found?.length) throw new Error(`Could not find policy after upsert for ${farmer.name}`);
    policyId = found[0].id;
    log("policy fetch fallback", true, `id=${policyId.slice(0,8)} policyNum=${policyNumber}`);
  }

  // M13 GUARD: M13/NAU sync is the source of truth for crop_coverage, drp, lrp,
  // and prf data, so those child writes are skipped for M13-owned policies.
  //
  // EXCEPTION — pp_eligibility: neither sync EVER writes this table (they only
  // DELETE it for non-crop policies during phantom cleanup, see m13-sync.js /
  // nau-sync.js). A PP Eligibility worksheet uploaded by hand is therefore the
  // ONLY source for PP data, even on MP-/NAU policies. Blanket-skipping it (the
  // old behavior) silently dropped every manual PP upload for R&H/NAU policies.
  // So for M13-owned policies we run the PP section below, then return BEFORE the
  // sync-owned sections. This is safe: the PP delete is scoped to this policy_id
  // and season, and M13's phantom cleanup only deletes PP for non-crop policies.
  if (isM13Owned) {
    log("m13_guard", true, `M13-owned ${policyNumber} — writing pp_eligibility only (sync owns crop_coverage/drp/lrp/prf)`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. PP ELIGIBILITY — ONLY if this import has PP data
  // GUARDRAIL: DRP/LRP/PRF imports MUST NOT touch pp_eligibility.
  // ═══════════════════════════════════════════════════════════════════════════
  const hasPPData = farmer.ppEligibility?.length > 0;
  if (hasPPData) {
    // Season-aware PP delete: only delete PP rows for the season being imported
    const ppIsSpring = farmer.ppEligibility.some(p => !FALL_CROPS.includes((p.crop||'').toUpperCase()));
    const ppIsFall   = farmer.ppEligibility.some(p =>  FALL_CROPS.includes((p.crop||'').toUpperCase()));

    if (ppIsSpring && !ppIsFall) {
      // Spring PP only — preserve fall PP rows
      const existingPP = await sbFetch("pp_eligibility", { filters: [`policy_id=eq.${policyId}`] });
      const springPPIds = (existingPP || [])
        .filter(r => !FALL_CROPS.includes((r.crop || '').toUpperCase()))
        .map(r => r.id).filter(Boolean);
      if (springPPIds.length > 0) await sbDelete("pp_eligibility", `id=in.(${springPPIds.join(',')})`);
    } else if (ppIsFall && !ppIsSpring) {
      // Fall PP only — preserve spring PP rows
      const existingPP = await sbFetch("pp_eligibility", { filters: [`policy_id=eq.${policyId}`] });
      const fallPPIds = (existingPP || [])
        .filter(r => FALL_CROPS.includes((r.crop || '').toUpperCase()))
        .map(r => r.id).filter(Boolean);
      if (fallPPIds.length > 0) await sbDelete("pp_eligibility", `id=in.(${fallPPIds.join(',')})`);
    } else {
      // Both seasons in import — safe to delete all
      await sbDelete("pp_eligibility", `policy_id=eq.${policyId}`);
    }
    // ⚠ THE WORKSHEET IS THE ONLY SOURCE THERE WILL EVER BE, so everything the
    // extractor reads has to have somewhere to land. Kevin, 2026-09-28: "PP eligibility
    // will always be a manual PDF upload from each AIP since it doesn't come in on m13
    // data." That was established against RMA's own P11 spec, which carries acres
    // REPORTED as prevent plant and nothing for a maximum or an eligibility
    // determination — there is no sync that could ever fill this table.
    //
    // ⚠ AND ONE COLUMN WAS BEING READ AND DISCARDED. Rain & Hail's batch report prints
    // TWO acreage figures per crop and county — Max Eligible and Remaining Eligible —
    // and the extraction prompt has asked for both since it was written. Only the first
    // had a column. `remainingEligAcres` appeared in exactly ONE place in this file: the
    // prompt that asks for it. On the prompt's own worked example (Atwater Farms,
    // WHEAT/NIAGARA) the discarded figure was 85.50 against a stored 609.50.
    //
    // It is also the one we cannot derive. ¶26C(1)(a)'s four-year maximum is checkable
    // and checks out — src/claims/ppEligibility.js reproduces `max_acres` on all 945
    // rows carrying both a history and a figure — but ¶26C(4) subtracts timely and late
    // PLANTED acres, and re-deriving those from crop_coverage_units is what produced
    // three false positives in the book report (that table carries SCO/ECO overlay rows
    // restating the same physical acres, so a planted total off it runs 2-3x high).
    //
    // ⚠ `??` NOT `||`, BECAUSE ZERO IS A REAL ANSWER. "No eligible acres remain" is what
    // forces a prevent-plant claim to roll to another crop under ¶26C(9); turned into
    // null it reads as "not stated" and the claim looks unconstrained.
    const aipForPp = (farmer.aip && farmer.aip !== "Multiple") ? farmer.aip : null;
    const ppBaseRow = pp => ({
      policy_id:      policyId,
      // ⚠ NOT defaulted to NAU the way policyPayload.aip is. This column exists to say
      // WHICH carrier's worksheet a row came from — 385 rows carry none, which is why
      // legacy uploads could not be told from current ones — and a guessed carrier is
      // worse than an unknown one.
      aip:            aipForPp,
      county:         normalizeCounty(pp.county)   || null,
      crop:           pp.crop     || null,
      // ⚠ NULL, NOT "". `|| ""` is the writer that made `== null` lie: 196 rows carry an
      // empty string, so a completeness probe testing for null reports zero missing and
      // the column reads fully populated. "Not stated" is NULL.
      practice:       pp.practice || null,
      type:           pp.type     || null,
      remaining_acres: pp.remainingEligAcres ?? null,
      yr1:            pp.yr1      ?? null,
      yr1_year:       pp.yr1Year  ?? null,
      yr2:            pp.yr2      ?? null,
      yr2_year:       pp.yr2Year  ?? null,
      yr3:            pp.yr3      ?? null,
      yr3_year:       pp.yr3Year  ?? null,
      yr4:            pp.yr4      ?? null,
      yr4_year:       pp.yr4Year  ?? null,
      max_acres:      pp.maxEligAcres ?? null,
      max_acres_year: pp.maxEligYear  ?? null,
      planted_acres:  pp.plantedAcres ? parseFloat(pp.plantedAcres) : null,
      aph_yield:            pp.aphYield          ?? null,
      pp_coverage_level:    pp.coverageLevel     ?? null,
      pp_liability_per_acre: pp.ppLiabilityPerAcre ?? null,
    });
    const ppRows = farmer.ppEligibility.map(ppBaseRow);
    try {
      await sbInsert("pp_eligibility", ppRows);
      log("pp_eligibility", true, `${ppRows.length} rows`);
    } catch(ppErr) {
      // If extended columns don't exist in this DB schema, retry with base columns only
      if (ppErr.message.includes("column") || ppErr.message.includes("42703")) {
        // ⚠ THIS RETRY DROPS THE *EXTENDED* COLUMNS (aph_yield, pp_coverage_level,
        // pp_liability_per_acre) FOR A DATABASE THAT PREDATES THEM. It must NOT drop an
        // acreage figure: `remaining_acres` is a peer of `max_acres`, it is the AIP's own
        // ¶26C(4) determination, and nothing else we hold can reconstruct it. If a
        // database genuinely lacks that column the retry fails too — and a loud failure
        // is the right outcome, because a silent drop here is the exact defect this
        // whole change exists to fix.
        const ppBaseOnly = farmer.ppEligibility.map(pp => ({
          policy_id: policyId, aip: aipForPp,
          county: normalizeCounty(pp.county)||null, crop: pp.crop||null,
          practice: pp.practice||null, type: pp.type||null,
          remaining_acres: pp.remainingEligAcres ?? null,
          yr1: pp.yr1??null, yr1_year: pp.yr1Year??null,
          yr2: pp.yr2??null, yr2_year: pp.yr2Year??null,
          yr3: pp.yr3??null, yr3_year: pp.yr3Year??null,
          yr4: pp.yr4??null, yr4_year: pp.yr4Year??null,
          max_acres: pp.maxEligAcres??null, max_acres_year: pp.maxEligYear??null,
          planted_acres: pp.plantedAcres ? parseFloat(pp.plantedAcres) : null,
        }));
        await sbInsert("pp_eligibility", ppBaseOnly);
        log("pp_eligibility", true, `${ppBaseOnly.length} rows (base columns only — run DB migration to add aph_yield, pp_coverage_level, pp_liability_per_acre)`);
      } else {
        throw ppErr;
      }
    }
  }

  // M13-owned policies: pp_eligibility (above) is the only manually-writable
  // child table — stop here so we never clobber sync-owned crop_coverage /
  // SCO-ECO / drp / lrp / prf data.
  if (isM13Owned) {
    return { steps, farmerId, policyId };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. CROP COVERAGE (approved yields + SCO/ECO) — ONLY if this import has crop data
  // GUARDRAIL: DRP-only, LRP-only, PRF-only imports MUST NOT touch crop_coverage.
  // Even if the merged farmer object carries forward approvedYields from a previous
  // import, we only delete+re-insert if this import is actually a crop import.
  // ═══════════════════════════════════════════════════════════════════════════
  const hasCropYields = farmer.approvedYields?.length > 0;
  const isCropImport = hasSpringCropData || hasFallCropData || hasScoEcoData;

  if (isCropImport && hasCropYields) {
    // ═══════════════════════════════════════════════════════════════════════
    // CRITICAL SILO FIX: Do NOT delete all crop_coverage for this policy.
    // Spring imports must only delete spring crop rows. Fall imports must
    // only delete fall crop rows. A blanket delete wipes the other season's
    // data when both spring and fall crops share the same policy_id.
    // ═══════════════════════════════════════════════════════════════════════
    if (hasSpringCropData && !hasFallCropData) {
      // Spring-only import: delete only non-fall crop rows
      // We fetch existing rows, identify spring ones, and delete by id
      const existingCov = await sbFetch("crop_coverage", { filters: [`policy_id=eq.${policyId}`] });
      const springRowIds = (existingCov || [])
        .filter(r => !FALL_CROPS.includes((r.crop || '').toUpperCase()))
        .map(r => r.id)
        .filter(Boolean);
      if (springRowIds.length > 0) {
        await sbDelete("crop_coverage", `id=in.(${springRowIds.join(',')})`);
        log("crop_coverage_delete", true, `Deleted ${springRowIds.length} SPRING crop_coverage rows for policy ${policyId.slice(0,8)} (fall rows preserved)`);
      } else {
        log("crop_coverage_delete", true, `No existing spring rows to delete for policy ${policyId.slice(0,8)}`);
      }
    } else if (hasFallCropData && !hasSpringCropData) {
      // Fall-only import: delete only fall crop rows
      const existingCov = await sbFetch("crop_coverage", { filters: [`policy_id=eq.${policyId}`] });
      const fallRowIds = (existingCov || [])
        .filter(r => FALL_CROPS.includes((r.crop || '').toUpperCase()))
        .map(r => r.id)
        .filter(Boolean);
      if (fallRowIds.length > 0) {
        await sbDelete("crop_coverage", `id=in.(${fallRowIds.join(',')})`);
        log("crop_coverage_delete", true, `Deleted ${fallRowIds.length} FALL crop_coverage rows for policy ${policyId.slice(0,8)} (spring rows preserved)`);
      } else {
        log("crop_coverage_delete", true, `No existing fall rows to delete for policy ${policyId.slice(0,8)}`);
      }
    } else {
      // Import has BOTH spring and fall crops — safe to delete all and re-insert
      await sbDelete("crop_coverage", `policy_id=eq.${policyId}`);
      log("crop_coverage_delete", true, `Deleted all crop_coverage for policy ${policyId.slice(0,8)} (import contains both spring + fall crops)`);
    }
  } else if (!isCropImport) {
    log("crop_coverage_skip", true, `SKIPPED — not a crop import (silos: [${siloFlags.join(', ')}]). Existing crop_coverage untouched.`);
  }
  if (isCropImport && hasCropYields) {
    const covRows = farmer.approvedYields.map(ay => ({
      policy_id:      policyId,
      county:         normalizeCounty(ay.county)        || null,
      crop:           ay.crop          || null,
      plan:           ay.plan          || null,
      practice:       ay.practice      || null,
      type:           ay.type          || null,
      aph_yield:      ay.approvedYield ?? null,
      coverage_level: ay.coverageLevel ?? null,
      price_election: ay.priceElection ?? null,
      acres:          ay.acres         ?? null,
      unit_count:     ay.unitCount     ?? 1,
      total_premium:  ay.totalPremium  ?? null,
      subsidy:        ay.subsidy       ?? null,
      net_premium:    ay.netPremium    ?? null,
      liability:      ay.liability     ?? null,
    }));
    try {
      await sbInsert("crop_coverage", covRows);
      log("crop_coverage", true,
        `${covRows.length} rows — crops: ${[...new Set(covRows.map(r => r.crop))].join(",")} — counties: ${[...new Set(covRows.map(r => r.county))].join(",")}`
      );
    } catch (covErr) {
      // If premium columns don't exist yet (schema migration pending), retry without them
      if (covErr.message?.includes("column") || covErr.message?.includes("42703")) {
        const covBaseRows = covRows.map(({ total_premium, subsidy, net_premium, liability, ...rest }) => rest);
        await sbInsert("crop_coverage", covBaseRows);
        log("crop_coverage", true,
          `${covBaseRows.length} rows (base columns — run DB migration to add total_premium/subsidy/net_premium/liability) — crops: ${[...new Set(covBaseRows.map(r => r.crop))].join(",")} — counties: ${[...new Set(covBaseRows.map(r => r.county))].join(",")}`
        );
      } else {
        throw covErr;
      }
    }
  } else {
    log("crop_coverage", false, `0 rows — approvedYields was empty or missing`);
  }
  // SCO/ECO endorsements stored in crop_coverage with plan='SCO-RP' or 'ECO-RP', aph_yield=null
  if (isCropImport && farmer.scoEcoEndorsements?.length) {
    const scoRows = farmer.scoEcoEndorsements.map(e => ({
      policy_id:      policyId,
      county:         normalizeCounty(e.county) || null,
      crop:           e.crop          || null,
      plan:           e.plan          || null,
      practice:       e.practice      || null,
      type:           e.type          || null,
      aph_yield:      null,
      coverage_level: e.coverageLevel ?? null,
      price_election: null,
      acres:          e.acres         ?? null,
      unit_count:     1,
      total_premium:  e.totalPremium  ?? null,
      net_premium:    e.netPremium    ?? null,
      liability:      e.liability     ?? null,
    }));
    await sbInsert("crop_coverage", scoRows);
    log("sco_eco", true, `${scoRows.length} rows — ${scoRows.map(r=>r.county+"/"+r.crop+"/"+r.plan).join(", ")}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 5. DRP ENDORSEMENTS — ONLY if this import has DRP data
  // GUARDRAIL: Crop/LRP/PRF imports MUST NOT touch drp_endorsements.
  // ═══════════════════════════════════════════════════════════════════════════
  const farmerIds = farmer.allFarmerDbIds?.length ? farmer.allFarmerDbIds : [farmerId];
  log("allFarmerIds", true, `ids=${farmerIds.join(",").slice(0,40)} (${farmerIds.length} total)`);

  if (hasDRPData) {
    await sbDelete("drp_endorsements", `policy_id=eq.${policyId}`);
    const drpRows = [];
    farmer.drpPolicies.forEach(pol => {
      pol.endorsements?.forEach(e => {
        drpRows.push({
          policy_id:          policyId,
          end_num:            e.endNum            ?? null,
          quarter:            e.quarter            || null,
          type:               e.type               || null,
          practice_type:      e.practiceType       || null,
          effective_date:     e.effectiveDate      || null,
          end_date:           e.endDate            || null,
          ins_shr:            e.insShr             ?? null,
          declared_milk_prod: e.declaredMilkProd   ?? null,
          exp_milk_per_cow:   e.expMilkPerCow      ?? null,
          class_iii:          e.classIII           ?? null,
          coverage_level:     e.coverageLevel      ?? null,
          projected_price:    e.projectedPrice     ?? null,
          trigger_price:      e.triggerPrice       ?? null,
          exp_rev_guar:       e.expRevGuar         ?? null,
          liability:          e.liability          ?? null,
          total_premium:      e.totalPremium       ?? null,
          subsidy:            e.subsidy            ?? null,
          producer_premium:   e.producerPremium    ?? null,
          // Stamp the DRP crop year (reinsurance year, rolls forward July 1) so
          // new DRP sold Jul 2026+ is written to CY2027, matching the sync. Derived
          // from the endorsement effective date — falls back to null if unparseable.
          soi_crop_year:      deriveDrpCropYear(e.effectiveDate) ?? null,
        });
      });
    });
    if (drpRows.length) {
      await sbInsert("drp_endorsements", drpRows);
      log("drp_endorsements", true, `${drpRows.length} rows`);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 6. LRP ENDORSEMENTS — ONLY if this import has LRP data
  // GUARDRAIL: Crop/DRP/PRF imports MUST NOT touch lrp_endorsements.
  // ═══════════════════════════════════════════════════════════════════════════
  if (hasLRPData) {
    await sbDelete("lrp_endorsements", `policy_id=eq.${policyId}`);
    const lrpRows = [];
    farmer.lrpPolicies.forEach(pol => {
      pol.endorsements?.forEach(e => {
        // Build the full desired row — we'll filter to only columns that exist in the table
        lrpRows.push({
          policy_id:        policyId,
          line_id:          e.lineId          ?? null,
          commodity:        e.commodity || pol.commodity || null,
          type:             e.type || pol.type           || null,
          county:           e.county || pol.county       || null,
          sales_date:       e.salesDate        || null,
          end_date:         e.endDate          || null,
          weeks_out:        e.weeksOut         ?? e.endorsementLength ?? null,
          head:             e.head             ?? e.numberOfHead      ?? null,
          target_weight:    e.targetWeight     ?? null,
          coverage_price:   e.coveragePrice    ?? null,
          insured_value:    e.insuredValue     ?? null,
          premium:          e.premium          ?? e.totalPremium      ?? null,
          producer_premium: e.producerPremium  ?? e.netPremium        ?? null,
          subsidy:          e.subsidy          ?? null,
          actual_end_value: e.actualEndValue   ?? null,
          payable_indemnity: e.payableIndemnity ?? null,
          billing_date:     e.billingDate      || null,
        });
      });
    });
    if (lrpRows.length) {
      // Discover actual table columns by reading one row (or empty set) with select=*
      let validCols = null;
      try {
        const probe = await sbFetch("lrp_endorsements", { select: "*" });
        if (probe.length > 0) {
          validCols = new Set(Object.keys(probe[0]));
        }
      } catch(e) { /* table might be empty or unreadable — try insert anyway */ }

      // If we couldn't discover columns, try a minimal safe set
      if (!validCols) {
        // Absolute minimum — just policy_id and the data fields most likely to exist
        validCols = new Set(["id","policy_id","sales_date","end_date","head","target_weight",
          "coverage_price","insured_value","premium","producer_premium","subsidy","actual_end_value"]);
        console.warn("LRP: could not discover table columns, using minimal safe set");
      } else {
        console.log("LRP: discovered table columns:", [...validCols].join(", "));
      }

      // Filter each row to only include columns that exist in the table
      const filteredRows = lrpRows.map(row => {
        const filtered = {};
        Object.entries(row).forEach(([k, v]) => {
          if (validCols.has(k)) filtered[k] = v;
        });
        return filtered;
      });

      try {
        await sbInsert("lrp_endorsements", filteredRows);
        log("lrp_endorsements", true, `${filteredRows.length} rows (${Object.keys(filteredRows[0]).length} cols)`);
      } catch(err) {
        console.error("LRP insert failed:", err.message, "Columns attempted:", Object.keys(filteredRows[0]));
        throw err;
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 7. PRF POLICIES + INTERVALS — ONLY if this import has PRF data
  // GUARDRAIL: Crop/DRP/LRP imports MUST NOT touch prf_policies or prf_intervals.
  // ═══════════════════════════════════════════════════════════════════════════
  if (hasPRFData) {
    // Discover PRF table columns
    let prfPolicyCols = null;
    let prfIntervalCols = null;
    try {
      const probe1 = await sbFetch("prf_policies", { select: "*" });
      if (probe1.length > 0) prfPolicyCols = new Set(Object.keys(probe1[0]));
    } catch(e) { /* empty table */ }
    try {
      const probe2 = await sbFetch("prf_intervals", { select: "*" });
      if (probe2.length > 0) prfIntervalCols = new Set(Object.keys(probe2[0]));
    } catch(e) { /* empty table */ }

    if (!prfPolicyCols) {
      prfPolicyCols = new Set(["id","policy_id","policy_number","county","coverage_level","productivity_factor","total_acres","total_liability","total_premium","net_premium","subsidy","billing_date"]);
      console.warn("PRF: could not discover prf_policies columns, using default set");
    } else {
      console.log("PRF: discovered prf_policies columns:", [...prfPolicyCols].join(", "));
    }
    if (!prfIntervalCols) {
      prfIntervalCols = new Set(["id","prf_policy_id","county","grid_id","interval_code","farm_unit","insured_acres","percent_value","liability","productivity_factor","premium"]);
      console.warn("PRF: could not discover prf_intervals columns, using default set");
    } else {
      console.log("PRF: discovered prf_intervals columns:", [...prfIntervalCols].join(", "));
    }

    const filterPrfCols = (row, validCols) => {
      const filtered = {};
      Object.entries(row).forEach(([k, v]) => { if (validCols.has(k)) filtered[k] = v; });
      return filtered;
    };

    // Delete intervals FIRST (child FK), then policies (parent) to avoid 409 Conflict
    // First find existing prf_policy IDs for this policy_id so we can delete their intervals
    try {
      const existingPrfPolicies = await sbFetch("prf_policies", { select: "id", filters: [`policy_id=eq.${policyId}`] });
      console.log("PRF: deleting intervals for", existingPrfPolicies.length, "existing prf_policies, policyId=", policyId);
      for (const pp of existingPrfPolicies) {
        await sbDelete("prf_intervals", `prf_policy_id=eq.${pp.id}`);
      }
    } catch(e) { console.warn("PRF: could not delete existing intervals:", e.message); }
    try {
      await sbDelete("prf_policies", `policy_id=eq.${policyId}`);
      console.log("PRF: deleted prf_policies for policyId=", policyId);
    } catch(e) { console.error("PRF: delete prf_policies FAILED:", e.message); }
    for (const prf of farmer.prfPolicies) {
      try {
        const prfRow = filterPrfCols({
          policy_id:           policyId,
          policy_number:       prf.policyNumber || null,
          county:              prf.county       || null,
          coverage_level:      prf.coverageLevel     ?? null,
          productivity_factor: prf.productivityFactor ?? null,
          total_acres:         prf.totalAcres    ?? null,
          total_liability:     prf.totalLiability ?? null,
          total_premium:       prf.totalPremium  ?? null,
          net_premium:         prf.netPremium    ?? null,
          subsidy:             prf.subsidy       ?? null,
          billing_date:        prf.billingDate   ?? null,
        }, prfPolicyCols);

        console.log("PRF: inserting prf_policy row:", JSON.stringify(prfRow));
        const prfPolicyRows = await sbInsert("prf_policies", [prfRow]);
        console.log("PRF: insert returned:", JSON.stringify(prfPolicyRows?.[0]));
        const prfPolicyId = prfPolicyRows?.[0]?.id;
        if (prfPolicyId && prf.intervals?.length) {
          const intervalRows = prf.intervals.map(iv => filterPrfCols({
            prf_policy_id:       prfPolicyId,
            county:              iv.county            || null,
            grid_id:             iv.gridId            || null,
            interval_code:       iv.intervalCode      || null,
            farm_unit:           iv.farmUnit          ?? null,
            insured_acres:       iv.insuredAcres      ?? null,
            percent_value:       iv.percentValue      ?? null,
            liability:           iv.liability         ?? null,
            productivity_factor: iv.productivityFactor ?? null,
            premium:             iv.premium           ?? null,
          }, prfIntervalCols));
          console.log("PRF: inserting", intervalRows.length, "intervals for prf_policy", prfPolicyId.slice(0,8));
          await sbInsert("prf_intervals", intervalRows);
          console.log("PRF: intervals inserted successfully");
          log("prf_intervals", true, `${intervalRows.length} intervals for prf_policy ${prfPolicyId.slice(0,8)}`);
        }
        log("prf_policies", true, `policy_id=${prfPolicyId?.slice(0,8)} county=${prf.county} total_acres=${prf.totalAcres}`);
      } catch(err) {
        console.error("PRF save failed:", err.message);
        throw err;
      }
    }
  }

  return { farmerId, policyId, steps };
}

// ─────────────────────────────────────────────────────────────────────────────
// RMA COUNTY DATA — now sourced from sob_data table (Supabase)
// Hardcoded CY2025 + CY2026 constants were migrated to sob_data on 2026-04-24
// MapView fetches via sbFetch('sob_data', {...}) instead of reading constants
// ─────────────────────────────────────────────────────────────────────────────

const FIPS_TO_NAME_GLOBAL = {"36001":"Albany","36003":"Allegany","36005":"Bronx","36007":"Broome","36009":"Cattaraugus","36011":"Cayuga","36013":"Chautauqua","36015":"Chemung","36017":"Chenango","36019":"Clinton","36021":"Columbia","36023":"Cortland","36025":"Delaware","36027":"Dutchess","36029":"Erie","36031":"Essex","36033":"Franklin","36035":"Fulton","36037":"Genesee","36039":"Greene","36041":"Hamilton","36043":"Herkimer","36045":"Jefferson","36047":"Kings","36049":"Lewis","36051":"Livingston","36053":"Madison","36055":"Monroe","36057":"Montgomery","36059":"Nassau","36061":"New York","36063":"Niagara","36065":"Oneida","36067":"Onondaga","36069":"Ontario","36071":"Orange","36073":"Orleans","36075":"Oswego","36077":"Otsego","36079":"Putnam","36081":"Queens","36083":"Rensselaer","36085":"Richmond","36087":"Rockland","36089":"St. Lawrence","36091":"Saratoga","36093":"Schenectady","36095":"Schoharie","36097":"Schuyler","36099":"Seneca","36101":"Steuben","36103":"Suffolk","36105":"Sullivan","36107":"Tioga","36109":"Tompkins","36111":"Ulster","36113":"Warren","36115":"Washington","36117":"Wayne","36119":"Westchester","36121":"Wyoming","36123":"Yates"};

function decodeTopo(topology, objectCollection) {
  var arcs = topology.arcs, transform = topology.transform;
  function decodeArc(idx) {
    var rev = idx < 0, arc = arcs[rev ? ~idx : idx], coords = [], x = 0, y = 0;
    for (var i = 0; i < arc.length; i++) {
      x += arc[i][0]; y += arc[i][1];
      var px = x, py = y;
      if (transform) { px = px * transform.scale[0] + transform.translate[0]; py = py * transform.scale[1] + transform.translate[1]; }
      coords.push([px, py]);
    }
    if (rev) coords.reverse(); return coords;
  }
  function decodeRing(ring) {
    var coords = [];
    for (var i = 0; i < ring.length; i++) { var ac = decodeArc(ring[i]); coords = coords.concat(coords.length > 0 ? ac.slice(1) : ac); }
    return coords;
  }
  return objectCollection.geometries.map(function(geom) {
    var geometry;
    if (geom.type === 'Polygon') geometry = { type: 'Polygon', coordinates: geom.arcs.map(decodeRing) };
    else if (geom.type === 'MultiPolygon') geometry = { type: 'MultiPolygon', coordinates: geom.arcs.map(function(p) { return p.map(decodeRing); }) };
    else geometry = { type: geom.type, coordinates: geom.coordinates };
    var fips = String(geom.id).padStart(5, '0');
    // Preserve any source properties (e.g. town NAME) while keeping the county
    // fips/name authoritative (set last) so county callers are unaffected.
    return { type: 'Feature', geometry: geometry, properties: Object.assign({}, geom.properties || {}, { fips: fips, name: FIPS_TO_NAME_GLOBAL[fips] || fips }), id: fips };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIN SCREEN (Supabase Auth)
// ─────────────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [shake, setShake]       = useState(false);
  const [loading, setLoading]   = useState(false);
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    // restoreSession() is async — must await the boolean it resolves to.
    // Calling it synchronously (`if (restoreSession())`) returned the Promise
    // itself which is always truthy, so onLogin() fired on every mount and
    // anyone could walk into the Dashboard without authenticating.
    let cancelled = false;
    (async () => {
      const ok = await restoreSession();
      if (cancelled) return;
      if (ok) onLogin();
      else setRestoring(false);
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit() {
    if (!email || !password) { setError("Enter email and password"); return; }
    setLoading(true);
    setError("");
    try {
      await supabaseSignIn(email.trim(), password);
      onLogin();
    } catch (e) {
      setError(e.message || "Login failed");
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  }

  if (restoring) {
    return (
      <div style={{ minHeight: "100vh", background: "#1a2e1a", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#4a7a4a" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🍀</div>
          <div style={{ fontSize: 13 }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#1a2e1a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Plus Jakarta Sans','Helvetica Neue',sans-serif" }}>
      <div style={{ width: "min(380px, 92vw)", animation: shake ? "shake 0.4s ease" : "none" }}>
        <style>{`
          @keyframes shake {
            0%,100%{transform:translateX(0)}
            20%{transform:translateX(-10px)}
            40%{transform:translateX(10px)}
            60%{transform:translateX(-8px)}
            80%{transform:translateX(8px)}
          }
          @keyframes fadeIn {
            from{opacity:0;transform:translateY(16px)}
            to{opacity:1;transform:translateY(0)}
          }
        `}</style>
        <div style={{ textAlign: "center", marginBottom: 36, animation: "fadeIn 0.5s ease" }}>
          <img src="/clover-mark.png" alt="Clover Ag" style={{ width: 68, height: 68, display: "block", margin: "0 auto 12px" }} />
          <img src="/silas-wordmark-light.svg" alt="SILAS" style={{ width: 211, height: 56, display: "block", margin: "0 auto" }} />
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.16em", color: "#7aaa7a", textTransform: "uppercase", marginTop: 11 }}>by Clover Ag Risk Management</div>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, padding: "32px 28px", boxShadow: "0 20px 60px rgba(0,0,0,0.4)", animation: "fadeIn 0.6s ease 0.1s both" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a", marginBottom: 6 }}>Agent Portal</div>
          <div style={{ fontSize: 13, color: "#60735f", marginBottom: 24 }}>Sign in to continue</div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              placeholder="you@example.com"
              autoFocus
              autoComplete="email"
              style={{ width: "100%", padding: "12px 14px", border: `2px solid ${error ? "#dc3545" : "#e4ece4"}`, borderRadius: 8, fontSize: 15, outline: "none", boxSizing: "border-box", color: "#1a2e1a", transition: "border-color 0.15s" }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              placeholder="Enter password"
              autoComplete="current-password"
              style={{ width: "100%", padding: "12px 14px", border: `2px solid ${error ? "#dc3545" : "#e4ece4"}`, borderRadius: 8, fontSize: 15, outline: "none", boxSizing: "border-box", color: "#1a2e1a", transition: "border-color 0.15s" }}
            />
            {error && <div style={{ fontSize: 12, color: "#dc3545", marginTop: 6, fontWeight: 600 }}>{error}</div>}
          </div>
          <button onClick={handleSubmit} disabled={loading} style={{ width: "100%", padding: "13px", background: loading ? "#3a5a3a" : "#1a2e1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 800, cursor: loading ? "default" : "pointer", letterSpacing: "0.06em", opacity: loading ? 0.7 : 1 }}>
            {loading ? "SIGNING IN..." : "SIGN IN"}
          </button>
        </div>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: "#60735f", lineHeight: 1.5 }}>
          Forgot your password? Contact your Clover Ag agent at <a href="mailto:office@cloverag.com" style={{ color: "#2d6a2d", fontWeight: 700 }}>office@cloverag.com</a> or (585) 233-1066 for a reset.
        </div>
        <div style={{ textAlign: "center", marginTop: 14, fontSize: 11, color: "#4a6e4a" }}>
          Clover Ag Risk Management
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & HELPERS
// ─────────────────────────────────────────────────────────────────────────────
// The crop year the live book is on. DERIVED FROM THE DATA — never hand-edited.
// The app has to roll from one crop year to the next by itself; a literal here
// means every screen silently reports last year's book until someone remembers
// to bump it.
//
// Why not the calendar year, and why not a July-1 roll? Both jump early. The
// syncs pull the NEXT reinsurance year's file from July 1, which seeds
// forward-year shells — wheat/barley rows with null acres and null premium —
// months before that season exists. Today CY2027 already has 334 such rows and
// ZERO carrying premium. Anchoring on row existence would empty the book for
// half a year.
//
// The rule is "the crop year the book carries the most premium for". It needs no
// threshold and no date arithmetic: through 2026 the answer is 2026, and it
// becomes 2027 organically as next season's policies are keyed and overtake it,
// which is exactly when the agency starts calling it the 2027 book.
//
// `let`, not `const`, so the ~80 read sites pick up the adopted value on the next
// render without threading a prop through every screen.
// ── WHICH CROP YEAR SPEAKS FOR A CARD ─────────────────────────────────────
// Different crop years of one policy COLLAPSE onto a single farmer card, so
// every per-policy list merged onto that card has to agree about which year
// wins — and agree with the header. A year that has ARRIVED beats a forward
// year outright; only then does the newest win. Three surfaces had already been
// fixed one at a time with a bare `cropYr >` (card identity, approvedYields,
// scoEcoEndorsements) and the fourth, ppEligibility, was still order-dependent.
// One rule, every caller.
// ⚠ PREVENT-PLANT ELIGIBILITY IS THE OPPOSITE CASE, AND IT IS NOT AN EXCEPTION
// — it is a different fact about the data. The AIP publishes its PP report AHEAD
// of the crop year; acreage is not reported until 15 November. Measured
// 2026-09-15 across the live book:
//
//     CY2027 fall COVERAGE       350 rows, 0 carry acres      (0%)
//     CY2027 fall PP ELIGIBILITY 231 rows, 231 carry a max  (100%)
//
// So for coverage the forward year is an empty shell and an arrived year must
// win; for PP eligibility the forward year is the ONLY complete answer and the
// only forward-looking one — the wheat in the ground right now is CY2027, and
// CY2027 is what a grower's prevented acres would be measured against. Rank on
// the crop year alone, newest wins.
//
// SPRING NEEDS NO SPECIAL CASE and must not get one: no CY2027 spring PP report
// has been loaded, so no CY2027 spring rows exist, so spring resolves to CY2026
// on its own and starts showing CY2027 the day that report is loaded.
// Which crop year the PP rows on screen actually came from. Fall and spring sit
// on DIFFERENT years on the same card — fall is CY2027 the moment its report is
// loaded, spring stays CY2026 until its own arrives — so the tab says which,
// rather than leaving a reader to infer it from the acreage.
function ppRowsYear(rows) {
  let y = null;
  for (const r of rows || []) if (r && r.cropYear != null && (y == null || r.cropYear > y)) y = r.cropYear;
  return y;
}

function ppYearRank(cropYr) {
  return cropYr || 0;
}

function cardYearRank(cropYr) {
  const y = cropYr || 0;
  // No crop year at all ranks BELOW every real one. Without this, y=0 is not
  // greater than CURRENT_CROP_YEAR, so it fell into the ARRIVED tier and
  // outranked every forward year — found by the checker, not by reading.
  if (!y) return -1;
  return (y > CURRENT_CROP_YEAR ? 0 : 1e4) + y;
}

let CURRENT_CROP_YEAR = new Date().getFullYear();   // cold-start fallback only

// Adopt the live crop year from the loaded book. Called once per data load.
// Premium is the signal, never row count — see above.
function adoptCropYearFromBook(policies, coverage) {
  // The rule lives in src/cropYear/bookCropYear.js so the server applies the same
  // one (api/_book-crop-year.js): the year carrying the most premium.
  const best = BOOK_CY.pickBookCropYear(policies, coverage);
  if (best) CURRENT_CROP_YEAR = best;
  return CURRENT_CROP_YEAR;
}

// Fall crops — single source of truth. Everything NOT in this list is a spring crop.
// OATS is a spring-planted crop and is NOT included here.
const FALL_CROPS = ["WHEAT","BARLEY","TRITICALE","RYE","WINTER WHEAT"];
const FALL_CROPS_SET = new Set(FALL_CROPS);

// ── INSURANCE OPTIONS — the AIP's own election codes ────────────────────────
// `crop_coverage.insurance_options` is the UNION of the option codes across the
// coverage's APH databases, read out of P15 f[38] by both syncs and joined with
// commas. Only codes we have actually SEEN in the files get a name; anything else
// renders raw — the same convention the claim stage codes and corn type codes
// follow, and for the same reason: a label invented to fill a gap is
// indistinguishable from a sourced one to the next reader.
const INSURANCE_OPTION_NAME = {
  TA: "Trend Adjustment",
  YA: "Yield Adjustment (floor)",
  YC: "Yield Cup",
  YE: "Yield Exclusion",
  QL: "Quality Loss",
  MC: "Multi-County Enterprise Unit",
  CP: "Contract Price",
  PF: "Prevent Plant buy-up",
  EU: "Enterprise Unit",
};

// The stored string is NOT one format. Both syncs write "QL,TA,YA"; the hand-loaded
// books carry "EU, TA, YA, YC, QL" (spaced) and "EU01,QL,YA,YC" (code + a numeric
// suffix). Splitting on non-alpha handles all three and drops the digits, so EU01
// and EU are one code. Do not "tidy" this into a comma split.
function parseOptionCodes(raw) {
  return [...new Set(String(raw || "").toUpperCase().split(/[^A-Z]+/).filter(Boolean))];
}

// Does this policy number belong to one of the two SFTP syncs? R&H is MP-…, NAU is
// NY-944-1234567-26. Everything else was hand-loaded from a PDF, which decides
// what a blank field on its rows is allowed to MEAN — the file carries P15 f[38]
// and an SOI does not, so a blank on a synced row is "no option elected" while a
// blank on a hand-loaded one is "never captured". Format only: whether a
// sync-format policy is externally managed is a separate question, kept where it
// belongs in saveFarmerToSupabase.
function isSyncFormatPolicyNumber(pn) {
  const s = String(pn || "");
  return /^MP-/i.test(s) || /^[A-Z]{2}-\d{3}-\d+-\d+$/i.test(s);
}
// ── A POLICY THE AIP FILE NO LONGER NAMES ───────────────────────────
// A REPLACED policy does not announce itself. Its row stays valid, its child
// rows freeze at whatever the last sync that saw it wrote, and everything
// downstream reads it as live. Thomas Shipley holds two CY2026 NAU policies on
// Bedford corn: PA-942-6108100-26 (five unit rows, refreshed 2026-08-20, ties to
// the SOI, 82.09 PP-eligible acres) and PA-942-1019860-26 (one coverage row from
// 2026-03-08, no acres, no liability, no premium, no units, 95.8 PP-eligible
// acres). Prevent-plant eligibility is NOT additive across policies so both PP
// surfaces take the MAX — and the MAX picked the dead one. 13.71 acres of
// eligibility a grower does not have, on a card, looking entirely legitimate.
//
// WHY THE SYNCS CANNOT SEE IT: crop_coverage is delete-and-reinsert scoped to
// the policies found IN THE FILE, so a policy that disappears is never deleted
// and never refreshed. sql/042 adds `policies.file_last_seen_at`, which both
// syncs stamp for every policy the parsed file names, and this reads it.
//
// THREE RULES, and each one is the difference between correcting a number and
// blanking a card:
//
//  1. POSITIVE EVIDENCE ONLY. Nothing is superseded unless a SIBLING policy of
//     the same AIP and crop year carries a FRESHER stamp — i.e. we know that
//     book's file was read and did not name this policy. A NULL stamp on its
//     own decides nothing, so the whole book reads exactly as it does today
//     until both syncs have run once after deploy.
//  2. SYNC-FORMAT NUMBERS ONLY. A hand-loaded book (Agri Sompo, ProAg) has no
//     file to be absent from and can never be judged by this column.
//  3. IT NARROWS ELIGIBILITY, IT NEVER DELETES. The replaced policy is history:
//     its claims, premium and acreage stay exactly where they are. It just
//     stops voting on the CURRENT year. Measured 2026-08-21, 31 sync-format
//     policies are in this state and only Shipley's outranks a live sibling —
//     the other 14 crop-county pairs have NO live counterpart, so a rule that
//     dropped them outright would blank a card rather than correct it. That is
//     exactly why the callers below set a superseded row aside only when a live
//     row answers for the same crop and county, and REPORT what they set aside.
//
// The grace window is deliberately generous. Excluding a LIVE policy understates
// a grower's eligibility, which is the worse error of the two; a dead policy
// lingering a few more days costs nothing. Seven days also survives a weekend of
// failed crons and a partially-stamped run.
const SUPERSEDED_GRACE_MS = 7 * 24 * 60 * 60 * 1000;
function supersededPolicyIds(policies) {
  const rows = policies || [];
  const bookKey = p => `${String(p.aip || "").toUpperCase()}|${p.crop_year ?? ""}`;
  const stampOf = p => { const t = p.file_last_seen_at ? Date.parse(p.file_last_seen_at) : NaN; return Number.isFinite(t) ? t : null; };
  // Newest stamp per (AIP, crop year) — the last time we know that book's file was read.
  const newest = new Map();
  rows.forEach(p => {
    const ts = stampOf(p); if (ts == null) return;
    const k = bookKey(p);
    if (!newest.has(k) || ts > newest.get(k)) newest.set(k, ts);
  });
  const out = new Set();
  const nowMs = Date.now();
  rows.forEach(p => {
    const ref = newest.get(bookKey(p));
    if (ref == null) return;                                   // rule 1a — no stamped run at all, decide nothing
    // Rule 1b — THE EVIDENCE ITSELF HAS TO BE CURRENT, and this is the half the
    // first cut of this rule missed. `ref` is the newest stamp in the book, not
    // necessarily a RECENT one: if the freshest thing we have for an AIP and
    // crop year is months old, we have no current read of that file and cannot
    // say what it does or does not name. Without this, one stale stamp beside an
    // unstamped sibling condemned the UNSTAMPED one — the live policy — which is
    // the eager direction and the worse of the two errors: it understates a
    // grower's eligibility. Caught by tools/check-superseded-policy.js before it
    // shipped, which is the whole reason that checker asserts both ways.
    if (nowMs - ref > SUPERSEDED_GRACE_MS) return;
    if (!isSyncFormatPolicyNumber(p.policy_number)) return;    // rule 2 — hand-loaded, no file to be absent from
    const ts = stampOf(p);
    if (ts != null && ref - ts <= SUPERSEDED_GRACE_MS) return; // named by the current file
    out.add(p.id);
  });
  return out;
}

// ── WHAT HAS ALREADY ROLLED PAST THE BOOK YEAR ──────────────────────────────
// The book year above is ONE number, but the AIP files do not roll as one. From
// July they carry the next reinsurance year, and the lines of business cross
// that line at different times:
//   FALL crops   — CY2027 winter wheat is sold in autumn 2026. Real, now.
//   LIVESTOCK    — DRP/LRP roll July 1 by rule. Real, now (LIVESTOCK_POLICY_TYPES).
//   SPRING crops — nothing until next season's acreage is keyed.
//   PRF          — not sellable until the November sales closing.
// "Still CY2026 for everything except wheat and livestock" is the actual state
// of the book, not a simplification — as of 2026-08-13 CY2027 holds 344 fall
// rows, 0 spring rows and 0 PRF rows. A page that prints one year without
// saying so leaves the reader to infer the rest from a stray -27 policy number.
//
// Measured from the RAW rows at load time, never from the farmer objects:
// approvedYields dedupes a forward-year election away whenever the same cell
// already carries current-year coverage, so counting there undercounts badly.
// `keyed` and `premium` are counted here too, because the forward-year view has to
// say what the crop side actually holds rather than assert it. A rolled APH
// database is not coverage: as of 2026-08-17 all 345 CY2027 rows carry NULL acres
// and $0 premium. Counting keyed rows makes that a measurement that moves on its
// own once acreage reporting starts, instead of a sentence somebody has to
// remember to delete.
let FORWARD_YEAR = null;   // { year, fall, spring, keyed, premium, farms, crops[] } — null when nothing has rolled
function summarizeForwardYear(policies, coverage) {
  const yearOf = new Map(), farmerOf = new Map();
  for (const p of policies || []) if (p && p.id != null) { yearOf.set(p.id, p.crop_year); farmerOf.set(p.id, p.farmer_id); }
  const year = CURRENT_CROP_YEAR + 1;
  const crops = new Set(), farms = new Set();
  let fall = 0, spring = 0, keyed = 0, premium = 0;
  for (const c of coverage || []) {
    if (yearOf.get(c.policy_id) !== year) continue;
    const crop = String(c.crop || "").toUpperCase();
    if (crop) crops.add(crop);
    if (FALL_CROPS_SET.has(crop)) fall++; else spring++;
    if (c.acres != null) keyed++;
    premium += Number(c.total_premium) || 0;
    const fid = farmerOf.get(c.policy_id);
    if (fid) farms.add(fid);
  }
  FORWARD_YEAR = (fall + spring) > 0
    ? { year, fall, spring, keyed, premium, farms: farms.size, crops: [...crops].sort() }
    : null;
  return FORWARD_YEAR;
}

// Crops whose production-guarantee unit is reliably modeled by getCropPriceAndUnit
// (bu for grains, lbs for dry beans, cwt for potatoes/cabbage). Used to gate the
// bushel/production-guarantee badge on card summaries — every OTHER crop defaults to
// "bu" in getCropPriceAndUnit, which is wrong for specialty crops (grapes/apples/
// peaches/onions are tons), so those show acres only rather than a mislabeled figure.
const GUAR_UNIT_CROPS = new Set(["CORN","SOYBEANS","OATS","GRAIN SORGHUM","DRY BEANS","POTATOES","CABBAGE"]);

// Perennials (tree/vine fruit) — NOT spring crops. Grape & orchard policies live
// in crop_coverage alongside the row-crop book but are excluded from the Spring
// Crops review (Crop Review page), which is about annual field crops only.
const PERENNIAL_CROPS = ["GRAPES","APPLES","PEACHES","CHERRIES","PEARS","PLUMS","NECTARINES","APRICOTS","BLUEBERRIES"];
const PERENNIAL_CROPS_SET = new Set(PERENNIAL_CROPS);

// ── 2026 Prevent Plant Coverage Factors ──────────────────────────────────────
// ⚠ THE "2026+" IN THIS COMMENT IS CONTRADICTED BY THE AIP'S OWN LIABILITY.
// It read "EARP final rule (Nov 28 2025) removed PP buyup entirely for 2026+.
// One factor per crop." Measured 2026-09-02 against the one CY2026 wheat policy
// in the book that carries prevent-plant acres — ANCHOR FARMS INC,
// NY-944-6106687-26, ERIE, 57.06 ac all PP, APH 57, CL 0.50, liability $6,080:
//
//     6080 / (57.06 ac x 57 bu x 0.50 x $5.76) = 0.6491
//
// which is 60% + 5%, and his options string carries PF. That reproduces the
// independent reading already recorded in CLAUDE.md. So PP BUY-UP WAS IN EFFECT
// FOR CY2026 WHEAT; the removal appears to bite from 2027 (Kevin, 2026-08-11:
// "PF was NOT available for 2027"), which is what the ADM-driven
// wheatPpFactor2027 already reports through its own pfOffered flag.
//
// THE TABLE IS THE BASE FACTOR PER CROP AND DOES NOT CARRY THE BUY-UP TIER, and
// as of 2026-09-04 nothing pretends otherwise: it moved to src/ppFactor.js beside
// resolvePpFactor(), which reads the AIP's own stated guarantee first, RMA's
// published table second, and only then falls back to this. Do not restate
// "no buyup for 2026" without re-measuring: one row disproved it.
// PP_FACTORS and the base lookup now live in src/ppFactor.js, imported above, so
// there is ONE definition of the base table and ONE resolver that knows the
// buy-up sits on top of it. getPPFactor stays as the BASE-only alias every
// existing caller already means: it answers "what does RMA sell as standard",
// never "what does this policy carry". Anything that needs the second question
// calls resolvePpFactor() and gets a provenance and a band with it.
const getPPFactor = ppBaseFactor;

// The RMA liability stored on a crop_coverage row is BLENDED — planted acres at
// the full revenue guarantee + prevent-plant acres at the reduced PP factor. The
// revenue guarantee a grower cares about is on PLANTED acres only (PP has its own
// guarantee, shown on the Prevent Plant tab). Given the blended liability, the
// total acres, and the prevent-plant acres (authoritative from the AIP M13 feed —
// crop_coverage.pp_acres), split out the planted portion while preserving RMA's
// acre-weighting:  liability = fullGuarPerAc × (planted + pp × ppFactor)
//   → plantedRev = liability × planted / (planted + pp × ppFactor)
// Returns identity (no change) when there's no prevent-plant on the cell.
function plantedRevenueGuarantee(liability, acres, ppAcres, crop) {
  const A = Number(acres) || 0;
  const pp = Math.max(0, Number(ppAcres) || 0);
  if (pp <= 0) return { plantedAcres: acres == null ? null : A, plantedRev: liability == null ? null : Number(liability) };
  const planted = Math.max(0, A - pp);
  if (liability == null) return { plantedAcres: planted, plantedRev: null };
  const factor = getPPFactor(crop);
  const denom = planted + pp * factor;
  return { plantedAcres: planted, plantedRev: denom > 0 ? Number(liability) * planted / denom : 0 };
}

// ── ONE rule each for the two things the farmer card kept getting wrong ───────
//
// Both of these were hand-written in three places (spring table, fall table, PDF
// export) and only ever fixed in one at a time. That produced three separate
// production bugs on 2026-07-31 alone:
//   • SCO leaked across counties  — && bound tighter than || in one copy
//   • SCO nested under the wrong crop — crop guard present in 2 of 3 copies
//   • PDF showed insured acres     — PP split applied on screen, not in print
// Every one was the same defect: a rule that lives in more than one place drifts.
// Call these instead of re-deriving the logic.

// Which SCO/ECO endorsements belong to a base coverage row.
// county is NOT checked here — callers must pass rows already scoped to the
// county being rendered, because the county lives on the section, not the row.
// Blank type on the endorsement means it covers the WHOLE crop, which is why the
// crop guard is essential: fall crops share type 'W', so without it wheat's SCO
// nests under triticale.
//
// `baseRows` is the county's full base list and decides who OWNS an untyped
// endorsement. Without it, "blank matches any type" attached the same
// endorsement to every type of the crop, so a grower with both grain and silage
// corn saw one SCO listed twice — 67 endorsements across 31 farmers, $3,308,536
// of liability and $198,537 of premium rendered twice on a card (the county
// footer was always right; it sums the overlays once).
//
// One endorsement really does span both types — that is not an assumption. All
// 1,099 overlay unit rows in the book carry type 997 (no type specified), and
// the overlay's acreage equals the two types' planted acres ADDED: Anderson
// Livingston 718.88 = 154.42 grain + 564.46 silage; C & W Chautauqua 467.84 =
// 451.72 + 16.12. So it belongs to the crop, not to a type, and is shown once —
// against the FIRST base row of that crop+practice, which is stable because the
// caller's row order is stable.
function matchSupplementals(scoRows, ecoRows, row, baseRows) {
  const sameCell = (a, b) =>
    (a.crop || "").toUpperCase() === (b.crop || "").toUpperCase() &&
    normalizePractice(a.practice) === normalizePractice(b.practice);
  // Owns the untyped endorsements only if this is the first row of its crop+practice.
  // No baseRows passed → behave as before rather than silently hide an endorsement.
  const ownsUntyped = !Array.isArray(baseRows) || baseRows.indexOf(row) < 0 ||
                      baseRows.findIndex(r => sameCell(r, row)) === baseRows.indexOf(row);
  const fits = (s) =>
    sameCell(s, row) &&
    ((s.type || "") === (row.type || "") || ((s.type || "") === "" && ownsUntyped));
  return { sco: (scoRows || []).filter(fits), eco: (ecoRows || []).filter(fits), any(){ return this.sco.length > 0 || this.eco.length > 0; } };
}

// The planted-acre view of a coverage row: acres, liability and guarantee/acre
// with prevent plant removed. RMA's stored liability is BLENDED (planted acres at
// the full guarantee + PP acres at the reduced factor) — verified against
// Danielewicz's 2026 SOI, where the 125-acre PP unit carries $359.90/ac against
// $654.19 full, exactly 0.55. Guarantee per acre comes from that liability, which
// is acre-weighted across units the way the SOI shows; the APH × price × coverage
// estimate is only a fallback for rows with no liability keyed yet.
function plantedView(row, ppAcres, estGuarPerAcre, units) {
  // RMA's own planted number, whenever the per-unit rows can carry it (below).
  const exact = plantedFromUnits(row, units);
  if (exact) return exact;
  const pp = Math.max(0, Number(ppAcres) || 0);
  const split = plantedRevenueGuarantee(
    row.liability != null ? Number(row.liability) : null,
    row.acres != null ? Number(row.acres) : null, pp, row.crop);
  const acres = row.acres != null ? (pp > 0 ? split.plantedAcres : Number(row.acres)) : null;
  const liability = row.liability != null ? (pp > 0 ? split.plantedRev : Number(row.liability)) : null;
  const gpa = (liability != null && acres) ? liability / acres : (estGuarPerAcre ?? null);
  return { ppAcres: pp, acres, liability, gpa, exact: false };
}

// The units behind a coverage row with the prevent-plant ones removed. The spring
// and fall tables are the PLANTED-guarantee view — PP carries its own reduced
// guarantee on its own tab — so a PP unit must not reach the acre-weighted APH,
// the unit list, or its totals. Rich Harvest's Livingston corn read "203.3 wtd"
// weighted over 577.7 INSURED acres on a line that was otherwise showing 533.4
// planted, and the unit total read $336,744 against a $322,034 row.
function plantedUnits(units) { return (units || []).filter(u => !u.is_prevent_plant); }
function ppUnits(units)      { return (units || []).filter(u =>  u.is_prevent_plant); }

// RMA'S OWN PRODUCTION GUARANTEE for a coverage, summed from the units it states
// one for: Σ(P11 #120 Acre Guarantee Quantity × that unit's acres), PLANTED units
// only — prevent-plant units carry their own reduced guarantee and belong on the
// PP tab, the same split plantedUnits() enforces everywhere else.
//
// This is the figure guaranteeBushels() DERIVES as liability ÷ projected price.
// Where RMA states it, prefer the statement; the derivation stays as the fallback
// and as the cross-check, never as a silent substitute.
//
// Returns null rather than a number when nothing can be stated, and reports
// `units`/`of` so a caller can say "4 of 9 units" instead of presenting a partial
// sum as the whole coverage — a short guarantee reads as permission to forward
// sell bushels the policy does not cover, so it must never be silent. Same rule
// as grainBushelsFromCoverage()'s incomplete[].
//
// NAU ONLY today: Rain & Hail truncates its P11 at 93 fields and sends none of
// the output-only block, so every R&H unit returns null here.
function rmaStatedGuarantee(units) {
  const planted = plantedUnits(units);
  const stated = planted.filter(u => u.acre_guarantee_qty != null && Number(u.acres) > 0);
  if (!stated.length) return null;
  const qty = stated.reduce((s, u) => s + Number(u.acre_guarantee_qty) * Number(u.acres), 0);
  if (!(qty > 0)) return null;
  return { qty, units: stated.length, of: planted.length, complete: stated.length === planted.length };
}

// THE BUSHEL-GUARANTEE CELL, rendered once for both tables.
//
// SpringView and FallView each draw their own copy of the coverage table, and
// the first cut of this landed in FallView only — so the Spring Crops tab, which
// is what an agent actually opens, showed no change at all and I twice told
// Kevin it was a caching problem. Two copies of a cell is exactly the shape that
// produced the 2026-07-31 SCO bugs (a rule hand-written in three places, fixed
// in one). One function, two callers.
//
// `derived` is guaranteeBushels() — liability ÷ projected price, RMA's own
// acre-weighted Marketable Guarantee, but arithmetic WE do. `units` are the
// coverage's unit rows. Where the AIP STATES the guarantee (P11 #120, NAU only)
// that wins and is marked; otherwise the derived figure shows unmarked.
function BuGuarCell({ derived, units, unit, color = "#2d4a2d" }) {
  const stated = rmaStatedGuarantee(units);
  // A PARTIAL SUM MUST NOT SIT IN A TOTAL'S POSITION. When RMA states a
  // guarantee for only some of a coverage's units — Marquart's Wyoming corn is
  // 13 of 78 — Σ over those 13 is a real number for 13 units and a badly wrong
  // one for the crop. The first cut showed it with an amber "13/78" beside it,
  // which labels the number without stopping anyone reading it as the crop's
  // guarantee. So an incomplete statement is NOT used: the derived figure covers
  // the whole coverage and is shown instead, with the partial named on hover.
  // Same instinct as grainBushelsFromCoverage()'s incomplete[] — a guarantee
  // that reads short is permission to forward sell bushels that are not covered.
  const rma = (stated && stated.complete) ? stated : null;
  const shown = rma ? rma.qty : derived;
  if (shown == null) return <span style={{ color: "#ccc" }}>—</span>;
  // A disagreement between stated and derived is a FINDING, not a rounding
  // detail: a guarantee reading short is permission to forward sell bushels the
  // policy does not cover. Surface it rather than silently showing our pick.
  const drift = (rma && derived > 0) ? Math.abs(rma.qty - derived) / derived : 0;
  return (
    <span title={rma
      ? `RMA states ${fmt(rma.qty, 0)} ${unit} — each unit's Acre Guarantee Quantity (P11 field 120) x its acres`
        + (rma.complete ? "" : `, over ${rma.units} of ${rma.of} planted units`)
        + (derived != null ? `. Our derivation (liability / price) gives ${fmt(derived, 0)}.` : "")
      : "Derived: liability / projected price. This AIP does not send RMA's stated guarantee."}>
      <span style={{ fontWeight: 700, color }}>{fmt(shown, 0)}</span>
      <span style={{ fontSize: 10, color: "#aaa", fontWeight: 400, marginLeft: 2 }}>{unit}</span>
      {rma && isCurrentUserAdmin() && (
        <span style={{ fontSize: 10.5, fontWeight: 800, color: "#1d5c3f", background: "#e6f1ec",
                       border: "1px solid #bcdccc", borderRadius: 3, padding: "1px 4px",
                       marginLeft: 5, letterSpacing: "0.04em", verticalAlign: "middle" }}>RMA</span>
      )}
      {rma && !rma.complete && (
        <span style={{ fontSize: 10, fontWeight: 700, color: "#9a5a1a", marginLeft: 3 }}
              title={`RMA states a guarantee for ${rma.units} of ${rma.of} planted units — this total covers only those.`}>
          {rma.units}/{rma.of}
        </span>
      )}
      {drift > 0.005 && (
        <span style={{ fontSize: 10, fontWeight: 800, color: "#8c2f2c", marginLeft: 3 }}
              title={`RMA states ${fmt(rma.qty, 0)}; liability / price gives ${fmt(derived, 0)} — a ${(drift * 100).toFixed(1)}% difference worth checking against the SOI.`}>
          ≠{(drift * 100).toFixed(0)}%
        </span>
      )}
    </span>
  );
}

// The exact planted liability, summed from the units RMA actually insured.
// plantedRevenueGuarantee() MODELS the split at the PP factor, which assumes ONE
// uniform guarantee/acre across the whole cell — true only when every unit shares
// an APH. Rich Harvest's 39.4 PP acres sit on the 210-bu unit, well above the
// cell's 193.8, so their real PP liability is larger than a uniform split assumes
// and the model returned $322,034 where the units sum to $321,672.
//
// Refuses (returns null, caller falls back to the model) unless the units
// reconcile to the stored acres AND every planted unit carries acres + liability.
// Half-synced unit rows must never silently understate a guarantee.
function plantedFromUnits(row, units) {
  const us = units || [];
  const pps = ppUnits(us);
  if (!pps.length) return null;              // no PP → the summary row is already planted-only
  const totAc = us.reduce((s, u) => s + (Number(u.acres) || 0), 0);
  const stored = Number(row.acres);
  if (!(totAc > 0) || !(stored > 0)) return null;
  if (Math.abs(totAc - stored) > Math.max(0.5, stored * 0.005)) return null;   // units don't account for the cell
  const planted = plantedUnits(us);
  if (!planted.length) return null;
  if (planted.some(u => u.acres == null || u.liability == null)) return null;
  const acres     = planted.reduce((s, u) => s + Number(u.acres), 0);
  const liability = planted.reduce((s, u) => s + Number(u.liability), 0);
  if (!(acres > 0)) return null;
  return {
    ppAcres: +(totAc - acres).toFixed(2),
    acres: +acres.toFixed(2),
    liability: +liability.toFixed(2),
    gpa: liability / acres,
    exact: true,
  };
}

// ── THE canonical bushel (production) guarantee ──────────────────────────────
// RMA liability ÷ price = the SOI "Marketable Guarantee" ÷ price, i.e. the acre-weighted
// production guarantee exactly as the Schedule of Insurance states it. This is the ONE
// method the farmer card AND the Claim Manager must share — they diverged once (the card
// used liability, the Claim Manager recomputed APH×cov×acres off the stored UNWEIGHTED
// unit-average APH, so Kludt Monroe's 8-unit wheat read 17,006 vs the SOI's 16,851). Pass
// the liability + acres you want counted (PP-removed *planted* values for the farmer card's
// planted guarantee; full blended values to match the SOI). Fallback to APH×cov×acres only
// for rows with no liability keyed yet. Route every guarantee-bushel calc through here.
// ⚠ THE PRICE ELECTION IS PART OF THE DIVISOR. RMA writes liability at
//   acres × APH × coverage level × price × PRICE ELECTION PERCENT.
// Dividing liability by the price ALONE therefore assumes a 100% election, and
// silently returns a guarantee short by (1 − pct) on every row that elected less.
//
// That is not a hypothetical. Found 2026-08-19 when RMA's own stated guarantee
// (P11 #120) disagreed with this function by 82% on Marquart Bros' Wyoming
// potatoes: CAT coverage, 50% level and a 55% price election, so RMA's
// $17.85/cwt is written at $9.82 and 119,118 cwt read as 65,533. The card had
// been showing that short number, and nothing caught it because $9.82 is not a
// published price — it only appears once you divide the two figures.
//
// Book-wide at the time: 36 CY2026 CAT coverages on 9 policies and $3.95M of
// liability across apples, corn, dry beans, oats, peaches, potatoes, soybeans,
// triticale and wheat, plus 42 buy-up rows electing 50–90%.
//
// A GUARANTEE THAT READS SHORT IS A FORWARD-SELL CEILING THAT READS SHORT, so
// this is not a cosmetic rounding issue — it is the number the grain tool turns
// into "bushels you may sell".
//
// `priceElectionPct` is REQUIRED and guarded by tools/check-shared-rules.js: a
// 5-argument call is the old, wrong behaviour and must fail the checker rather
// than default quietly. Pass crop_coverage.price_election_pct (1 on every
// non-CAT row; measured present on all 36 CAT rows, none null, none wrongly 1).
// The APH × coverage × acres FALLBACK needs no election — it is the production
// guarantee directly, which is why it was right all along.
// THE ELECTED PRICE — the divisor a liability was actually written at.
//
// RMA writes liability at acres × APH × coverage × price × PRICE ELECTION PERCENT,
// so any "bushels" figure derived from liability must divide by price × election,
// never by the price alone. `lbs` crops are quoted per hundredweight, hence the
// ×0.01.
//
// This exists because the same three-line expression was written out in SEVEN
// places and only THREE of them applied the election. The three that did carry a
// comment describing the exact symptom — "keeps a CAT bushel guarantee from
// reading ~45% low (verified vs Mehlenbacher's CAT SOI)" — while the shared
// guaranteeBushels() and four other surfaces stayed wrong. The knowledge was in
// the codebase and the bug shipped anyway, which is the whole argument for one
// function instead of a remembered idiom.
const electedPrice = (price, unit, row) => {
  const base = unit === "lbs" ? Number(price) * 0.01 : Number(price);
  if (!(base > 0)) return 0;
  const raw = Number(row?.priceElectionPct ?? row?.price_election_pct);
  return base * (raw > 0 ? raw : 1);
};

// ── RMA'S OWN STATED PRODUCTION GUARANTEE — and how R&H reaches PARITY ───────
// P11 #120 `Acre Guarantee Quantity` x acres. RMA states this PER UNIT and it
// already carries the PP factor and the 1%/day late-planting slide SILAS cannot
// model. NAU sends #120; Rain & Hail does not, and cannot — RMA marks #94+
// "* Output Only", so the AIP never authors them (see the R&H correction in
// CLAUDE.md). That looked like a permanent asymmetry and is not one:
//
//   on a TIMELY unit, #120 is identically approved yield x coverage level,
//   and BOTH are inside the 93 fields R&H does send.
//
// Measured against RMA's own stated value on 1,211 timely NAU units (2026-08-20):
// exact, median 0.000% off, 1,211/1,211 within 0.5% — CAT and buy-up alike.
// The path it replaces, liability / price, runs 0.351% HIGH on CAT because RMA
// ROUNDS the CAT price and we multiply: Bainbridge's SOI prints corn CAT at
// $2.55 where 4.62 x 0.55 = 2.541. Wheat was the worst at 2.08%. Dollars were
// never affected — liability is RMA's own number — only the BUSHEL conversion,
// and it erred HIGH, which on a pre-harvest sell ceiling is the wrong direction.
//
// Two refusals are load-bearing, both in the same direction:
//  - PP units are excluded, not summed. They carry their own reduced guarantee
//    and grow no crop, so they buy nothing to sell.
//  - If ANY planted unit cannot be stated, the WHOLE answer is null and the
//    caller falls back. A partial sum reads as a SHORT guarantee, which reads as
//    over-sold. That is the one direction this tool must never be wrong in.
function statedGuaranteeBu(units) {
  if (!Array.isArray(units) || !units.length) return null;
  const planted = units.filter(u => !u.is_prevent_plant);
  if (!planted.length) return null;
  let total = 0;
  for (const u of planted) {
    const ac = Number(u.acres) || 0;
    if (!(ac > 0)) return null;
    const q = Number(u.acre_guarantee_qty) || 0;
    if (q > 0) { total += q * ac; continue; }   // RMA stated it outright (NAU)
    // No #120. Only a TIMELY unit is derivable — an adjusted one (L/M/P/E) carries
    // a factor that is NOT in the file, and returning a full guarantee on those
    // acres would overstate exactly where the grower is already short.
    if (u.acreage_status) return null;
    const aph = Number(u.aph_yield) || 0, cov = Number(u.coverage_level) || 0;
    if (!(aph > 0 && cov > 0)) return null;
    total += aph * cov * ac;
  }
  return total > 0 ? total : null;
}
function guaranteeBushels(liability, price, aph, cov, acres, priceElectionPct, statedBu) {
  // RMA's own stated guarantee outranks anything derived — see statedGuaranteeBu().
  if (statedBu != null && Number(statedBu) > 0) return Number(statedBu);
  const pct = (priceElectionPct == null || !(Number(priceElectionPct) > 0))
    ? 1 : Number(priceElectionPct);
  const effectivePrice = price > 0 ? price * pct : 0;
  if (liability != null && effectivePrice > 0) return Number(liability) / effectivePrice;
  return (aph > 0 && cov > 0 && acres > 0) ? aph * cov * acres : null;
}

// ── PER-UNIT (SECTION) DETAIL ────────────────────────────────────────────────
// crop_coverage carries ONE row per (county, crop, plan, type, practice) — the
// summary line. crop_coverage_units carries the units behind it, exactly as RMA
// insures them: McIlroy's Genesee corn is 5 units, each with its own acres,
// approved yield and liability, which the summary can only report as "avg of 5".
//
// Key normalisation matters: crop_coverage stores type_code NULL for the "997"
// (unknown) sentinel and NULL for practice "003" (non-irrigated), while the unit
// rows keep the raw codes. Both sides collapse to "" here so a card row finds
// its own units instead of silently finding none.
const cuKey = (county, crop, plan, typeCode, practiceCode) => {
  const tc = (typeCode && typeCode !== "997") ? String(typeCode) : "";
  const pc = (practiceCode && practiceCode !== "003") ? String(practiceCode) : "";
  return `${normalizeCounty(county || "").toUpperCase().trim()}|${(crop || "").toUpperCase()}|` +
         `${(plan || "").toUpperCase()}|${tc}|${pc}`;
};

// ── THE prevent-plant / uninsurable acre maps ────────────────────────────────
// Keyed the way a coverage row is ACTUALLY identified — county + crop + TYPE +
// practice — which is the same identity the card groups its rows on. Keyed on
// crop+county alone (as this was until 2026-08-12) every sibling row of a crop
// shared ONE PP number, so a line with no prevent plant of its own had its
// sibling's subtracted from it:
//   • W Cleveland — ORGANIC corn's 79.1 PP acres came off the CONVENTIONAL corn
//     line: 239.10 ac / $141,726 rendered as 160.00 ac / $111,428.
//   • Anderson Farms Dairy — SILAGE's 41.46 PP came off the GRAIN line.
//   • Palladino & Carley — 71.97 organic soybean PP acres exceeded the whole
//     58.70-acre conventional line, zeroing it: $21,562 of guarantee read $0.
// The type half of the key must match the loader's `covIdent` (type, falling
// back to type_code) or a row looks up a key that was never built.
const ppKey = (county, crop, type, typeCode, practice) =>
  `${normalizeCounty(county || "").toUpperCase().trim()}|${(crop || "").toUpperCase()}|` +
  `${(String(type || "").trim() || String(typeCode || "")).toUpperCase()}|` +
  `${normalizePractice(practice).toUpperCase()}`;
const ppKeyOfRow = (r) => ppKey(r.county, r.crop, r.type, r.typeCode ?? r.type_code, r.practice);
const ppKeyOfCov = (c) => ppKey(c.county, c.crop, c.type, c.type_code, c.practice);
// Everything the key needs, so no caller re-derives the column list.
const PP_UI_SELECT = "crop,county,type,type_code,practice,practice_code,pp_acres,ui_acres";
const PP_UI_FILTER = "or=(pp_acres.not.is.null,ui_acres.not.is.null)";
function buildPpUiMaps(rows, cropFilter) {
  const pp = {}, ui = {};
  (rows || []).forEach(c => {
    if (cropFilter && !cropFilter(c)) return;
    const k = ppKeyOfCov(c);
    const p = Number(c.pp_acres) || 0; if (p > 0) pp[k] = (pp[k] || 0) + p;
    const u = Number(c.ui_acres) || 0; if (u > 0) ui[k] = (ui[k] || 0) + u;
  });
  return { pp, ui };
}

// The unit map, fetched once and keyed by cuKey. Module-level (not just a hook)
// because generatePolicyPDF is NOT a component and must resolve the planted split
// the SAME way the screen does — the last time that rule lived in two places, the
// PDF printed insured acres and blended liability while the screen printed planted
// (shipped to growers, fixed 2026-07-31).
async function fetchCoverageUnitsByKey(farmer, year) {
  const pids = (farmer.allPolicyIds || farmer._allPolicyIds || []);
  if (!pids.length) return {};
  // SCOPE TO THE CARD'S CROP YEAR. `allPolicyIds` spans every year the farmer has, and
  // cuKey carries no year — so the units of ALL years collapsed into one bucket and a
  // coverage drilled down into next year's databases as though they were its own. Kennedy
  // Farms' 2026 wheat listed THIRTEEN units: his two real 2026 databases plus all eleven
  // 2027 ones, which carry null acres and null liability and so slipped past every total.
  // 129 coverage keys across 81 farmers were merging years this way (1,040 unit rows).
  // The totals stayed right by luck — the extra rows are null — but the list was wrong,
  // and it is what hid the fact that our 2027 data is missing two of his databases.
  // A screen showing ONE crop year asks for that year's units. Without the argument the
  // card's year is used, which is what every caller before the Fall year view wanted.
  const yr = Number(year) || Number(farmer.cropYear) || CURRENT_CROP_YEAR;
  const rows = await sbFetch("crop_coverage_units", {
    select: "policy_id,crop_year,county,crop,plan,type_code,practice_code,unit_number,sub_unit," +
            "unit_structure,is_prevent_plant,acres,aph_yield,liability,total_premium,net_premium," +
            // RMA's OWN stated guarantee for the unit (P11 #120 Acre Guarantee
            // Quantity / #117 Total Guarantee Amount) plus the adjustment type
            // that explains a reduced one. NAU only — Rain & Hail truncates its
            // P11 at 93 fields and sends none of this block, so these are NULL
            // book-wide on R&H. The column shows "—" there rather than quietly
            // substituting our derived figure for a stated one.
            // acreage_status IS the guarantee adjustment type (P11 f[37]). It is
            // read here rather than a dedicated column because it carries the MIXED
            // rule for collapsed sub-field records; a second copy diverged on 144
            // rows the day it was added.
            "acre_guarantee_qty,rma_total_guarantee,acreage_status",
    filters: [`policy_id=in.(${pids.join(",")})`, `crop_year=eq.${yr}`],
  });
  const m = {};
  for (const r of (rows || [])) {
    const k = cuKey(r.county, r.crop, r.plan, r.type_code, r.practice_code);
    (m[k] = m[k] || []).push(r);
  }
  // Planted units first, then PP, each in unit-number order — the order the
  // Schedule of Insurance lists them.
  Object.values(m).forEach(a => a.sort((x, y) =>
    (x.is_prevent_plant ? 1 : 0) - (y.is_prevent_plant ? 1 : 0) ||
    String(x.unit_number || "").localeCompare(String(y.unit_number || "")) ||
    String(x.sub_unit || "").localeCompare(String(y.sub_unit || ""))));
  return m;
}

// ── THE FARM'S WHEAT APH DATABASES, FOR THE FALL CARD (BUG-096) ─────────────────────────
// Every crop year the farm has, because the card's rule (src/wheat/fallCardAph.js) weighs a
// year's databases by their OWN acres or, where a database has none yet, by last year's — so
// the year before the one shown is load-bearing, not context. One read for the card and its
// PDF, so the two cannot print different approved yields.
async function fetchFallWheatUnits(farmer) {
  const pids = (farmer && (farmer.allPolicyIds || farmer._allPolicyIds)) || [];
  if (!pids.length) return [];
  const rows = await sbFetch("crop_coverage_units", { select: FALL_APH_UNITS_SELECT,
    filters: [`policy_id=in.(${pids.join(",")})`, "crop=eq.WHEAT"] });
  return rows || [];
}
// Tagged with the farmer it was read FOR: unitsFor() refuses to hand one farm's databases to
// another, so A -> B -> A cannot resolve B's rows with A's databases while B's read is in flight.
// A failed read is an ERROR, never an empty farm — the card then shows no approved yield at all
// rather than the summary row, which is the value in question.
function useFallWheatUnits(farmer) {
  const [st, setSt] = useState({ farmerId: null, status: "resolving", units: null });
  useEffect(() => {
    let alive = true;
    setSt({ farmerId: farmer.id, status: "resolving", units: null });
    fetchFallWheatUnits(farmer)
      .then((units) => { if (alive) setSt({ farmerId: farmer.id, status: "ready", units }); })
      .catch(() => { if (alive) setSt({ farmerId: farmer.id, status: "error", units: null }); });
    return () => { alive = false; };
  }, [farmer.id]);
  return st;
}

function useCoverageUnits(farmer, year) {
  const [byKey, setByKey] = useState({});
  useEffect(() => {
    let alive = true;
    fetchCoverageUnitsByKey(farmer, year)
      .then(m => { if (alive) setByKey(m); })
      .catch(() => { if (alive) setByKey({}); });
    return () => { alive = false; };
  }, [farmer.id, farmer.cropYear, year]);
  return byKey;
}

// ── THE planted split. Every acres/liability figure in the spring + fall sections
// goes through here — rows, county headers, table footers, the crop rollup cards
// and the PDF. Takes the coverage row and the unit map; hands back planted acres,
// planted liability and guarantee/acre with prevent plant removed.
// Pass unitsByKey and it uses RMA's own per-unit sum; omit it and it falls back to
// the PP-factor model. A footer that models while its rows sum is a $362
// contradiction on one card, which is worse than either number alone.
function plantedSplit(row, ppAcres, unitsByKey) {
  const units = unitsByKey
    ? unitsByKey[cuKey(row.county, row.crop, row.plan,
                       row.typeCode ?? row.type_code, row.practiceCode ?? row.practice_code)]
    : null;
  return plantedView(row, ppAcres, null, units);
}

// Acre-weighted APH across a coverage's units — what RMA actually prices off.
// The stored aph_yield is an UNWEIGHTED mean of the unit yields, which drifts on
// multi-unit policies (F&W 11 units: 154.9 stored vs 172.1 implied by liability;
// Mark Frederick 4 units: 169.8 vs 204.2). Returns null when acres are missing,
// so callers fall back to the stored value rather than inventing one.
function weightedAph(units) {
  if (!units || units.length < 2) return null;
  let ac = 0, sum = 0;
  for (const u of units) {
    const a = Number(u.acres) || 0, y = Number(u.aph_yield);
    if (!(a > 0) || !Number.isFinite(y) || y <= 0) continue;
    ac += a; sum += y * a;
  }
  return ac > 0 ? +(sum / ac).toFixed(1) : null;
}

// ── The AIP's reconciliation, under the county total ────────────────────────
// FARMER PREMIUM IS NOT WHAT THE GROWER IS BILLED, and until 2026-08-26 nothing
// on this card said so. Anchor Farms' county total read $15,439 against a NAU
// statement of $8,178 due; the $7,381 gap was a prevent-plant indemnity NAU had
// already applied against his premium, plus $120 of administrative fees.
//
// The credits are the AIP's OWN P20A rows and the fee is OURS, so the fee shows
// its arithmetic ("4 × $30") instead of hiding inside a total — a wrong fee
// count has to read as a wrong fee count, never as a wrong balance. One rule for
// every surface: SpringView, FallView and the PDF all call buildBillingRule.
//
// ⚠ IT IS PER POLICY, NOT PER COUNTY, AND THAT WAS MEASURED. The first cut of
// this sat in each county table's footer. Anchor is single-county so it tied to
// his statement perfectly — and run over the whole CY2026 book it drove FIVE
// cards negative, because a credit pays down the POLICY while the claim's county
// only says where the loss happened. Fire Creek MP-0935914 spans Livingston and
// Ontario and its $6,167 credit is the whole policy's premium. So this renders
// beside the county tables, one row per policy, exactly like the statement's
// "Policy Totals" block that Kevin reads it against.
function PolicyBillingPanel({ farmer, crops, title, tone }) {
  const t = tone || { accent: "#2d5a10", bg: "#f4f8f4", rule: "#d0e4d0" };
  // Rows come from billingRowsForTab, NOT from approvedYields directly. The
  // overlays live in a different collection (scoEcoEndorsements) and reading
  // only the base one billed Anchor at $11,967 over 2 fee lines against a
  // statement of $15,439 over 4 — his two ECO-RP lines were simply missing,
  // while the county table above it, which unions both, was right.
  const groups = billingRowsForTab({
    approvedYields: farmer.approvedYields,
    scoEcoEndorsements: farmer.scoEcoEndorsements,
    crops, cropYear: farmer.cropYear,
  });
  if (!groups.length) return null;
  // The BILL is the whole policy, so premium and fees come from every coverage
  // it wrote, including any shown on another tab. Scoping the premium to this
  // tab while the credit stays policy-wide is precisely how the county version
  // went negative.
  const panels = groups.map(g => {
    const b = buildBillingRule({
      rows: g.rows, claims: farmer.claims, policyId: g.policyId,
      policyNumber: g.policyNumber, cropYear: farmer.cropYear, farmerPremium: g.farmerPremium,
    });
    return {
      pid: g.policyId, policyNumber: g.policyNumber, offTab: g.spansOtherTabs, b,
      // Payments join on the POLICY the AIP stamped them with — never on a crop
      // year. A fall crop is paid the previous autumn, so the payment's own file
      // year says nothing about which policy it settles.
      out: policyOutstanding({
        policyId: g.policyId, policyNumber: g.policyNumber, policyType: "crop",
        cropYear: farmer.cropYear, billed: b.balanceDue,
        payments: farmer.premiumPayments,
      }),
    };
  }).filter(p => p.b.hasAnything);
  if (!panels.length) return null;

  const lbl = { fontSize: 11, color: "#7a8a78", padding: "3px 0" };
  const val = { textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12, whiteSpace: "nowrap", padding: "3px 0" };
  const note = { fontSize: 10, color: "#a8b2a6", marginLeft: 6 };

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 10 }}>
        <SectionTitle style={{ margin: 0, border: "none", padding: 0 }}>🧾 {title || (isCurrentUserAdmin() ? "What the AIP will bill" : "Your premium bill")}</SectionTitle>
        <span style={{ fontSize: 10, color: "#a8b2a6" }}>per policy, as the statement totals it</span>
      </div>
      {panels.map(({ pid, b, policyNumber, offTab, out }) => {
        // The statement prints ONE loss credit per claim; we hold it as its
        // disbursements (M premium + F fee). Group so the line matches the bill,
        // and keep the split in the subtitle so it stays checkable against P20A.
        const byClaim = new Map();
        b.credits.forEach(c => {
          const k = c.claimNumber || "—";
          if (!byClaim.has(k)) byClaim.set(k, { claimNumber: c.claimNumber, crop: c.crop, stages: c.stages, amount: 0, parts: [] });
          const g = byClaim.get(k);
          g.amount += c.amount;
          g.parts.push(c);
        });
        return (
          <div key={pid} style={{ marginBottom: 12, background: t.bg, borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: t.accent, letterSpacing: "0.04em", marginBottom: 6 }}>
              {policyNumber || "policy"}
              {offTab && <span style={note}>policy total — includes coverage listed on another tab</span>}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={lbl}>Farmer premium<span style={note}>gross − subsidy</span></td>
                  <td style={{ ...val, color: "#1a2e1a", fontWeight: 700 }}>{fmtD0(b.farmerPremium)}</td>
                </tr>
                {b.fees > 0 && (
                  <tr>
                    <td style={lbl}>
                      Administrative fees
                      <span style={note}>
                        {b.feeLines.length} billed line{b.feeLines.length === 1 ? "" : "s"} × ${b.feeLines[0] && b.feeLines.every(l => l.fee === b.feeLines[0].fee) ? b.feeLines[0].fee : "—"}
                        {isCurrentUserAdmin() ? " · RMA schedule, not in the AIP file" : ""}
                      </span>
                    </td>
                    <td style={{ ...val, color: "#8a7a4a" }}>+{fmtD0(b.fees)}</td>
                  </tr>
                )}
                {[...byClaim.values()].map((g, i) => (
                  <tr key={`credit-${i}`}>
                    <td style={lbl}>
                      Claim credit applied
                      <span style={note}>
                        {g.claimNumber ? `claim ${g.claimNumber} · ` : ""}{g.crop || ""}
                        {g.stages ? ` · ${g.stages}` : ""} · {g.parts.map(p => `${p.kind} ${fmtD0(p.amount)}`).join(" + ")}
                      </span>
                    </td>
                    <td style={{ ...val, color: "#2d5a10", fontWeight: 700 }}>−{fmtD0(g.amount)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: `1px solid ${t.rule}` }}>
                  <td style={{ ...lbl, paddingTop: 7, fontWeight: 700, color: "#4a6e4a" }}>
                    Balance due
                    <span style={{ ...note, fontWeight: 400 }}>premium + fees − credits</span>
                  </td>
                  <td style={{ ...val, paddingTop: 7, fontWeight: 700, color: b.overCredited ? "#9a5a1a" : "#4a6e4a" }}>
                    {fmtD0(b.balanceDue)}
                    {/* Never clamped at $0 — a credit bigger than the bill means
                        the AIP cut a check for the excess, or our fee count is
                        short. Both want looking at, not rounding away. */}
                    {b.overCredited && <span style={{ ...note, color: "#9a5a1a" }}>credit exceeds the bill</span>}
                  </td>
                </tr>
                {/* WHAT THE GROWER HAS PAID — M13 record P12, the AIP's own rows.
                    Payments join on the POLICY; there is no due date in anything
                    we hold (billing_date is NULL on every coverage row of every
                    year), so this says OUTSTANDING and never "past due". */}
                {out.paid !== 0 && (
                  <tr>
                    <td style={lbl}>
                      Paid
                      <span style={note}>
                        {out.paymentCount} payment{out.paymentCount === 1 ? "" : "s"}
                        {out.lastPaymentDate ? ` · latest ${out.lastPaymentDate}` : ""}
                        {out.payments.some(p => !p.known) ? " · includes an unrecognised payment code" : ""}
                      </span>
                    </td>
                    <td style={{ ...val, color: "#2d5a10", fontWeight: 700 }}>−{fmtD0(out.paid)}</td>
                  </tr>
                )}
                <tr style={{ borderTop: `1px solid ${t.rule}` }}>
                  <td style={{ ...lbl, paddingTop: 7, fontSize: 11, fontWeight: 800, color: t.accent, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {out.state === "settled" ? "Settled" : out.state === "credit" ? "Credit balance" : "Balance outstanding"}
                    <span style={{ ...note, textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
                      {out.paid === 0 ? "nothing received against this policy yet" : "after payments received"}
                    </span>
                  </td>
                  <td style={{ ...val, paddingTop: 7, fontSize: 15, fontWeight: 800,
                               color: out.state === "credit" ? "#9a5a1a" : out.state === "settled" ? "#2d5a10" : t.accent }}>
                    {fmtD0(out.outstanding)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}
    </Card>
  );
}

// The expandable unit list under a coverage row. Acres and liability are shown
// exactly as insured — per unit, never blended — because that is the level the
// grower's Schedule of Insurance reports and the level a claim is settled at.
function CoverageUnitRows({ units, colSpan, yieldUnit }) {
  // PLANTED units only — this table sits under the planted-guarantee row, so its
  // total has to equal that row. The PP units are summarised on one line below.
  const planted = plantedUnits(units);
  const pps = ppUnits(units);
  if (!planted.length) return null;
  const th = { padding: "4px 10px", fontSize: 10.5, letterSpacing: "0.06em", color: T.faint,
               fontWeight: 800, textAlign: "right", whiteSpace: "nowrap" };
  const td = { padding: "5px 10px", fontSize: 12, textAlign: "right",
               fontVariantNumeric: "tabular-nums", color: T.ink };
  const sum = (arr, f) => arr.reduce((s, u) => s + (Number(f(u)) || 0), 0);
  const tot = { acres: sum(planted, u => u.acres), liab: sum(planted, u => u.liability),
                prem: sum(planted, u => u.total_premium) };
  const ppTot = { acres: sum(pps, u => u.acres), liab: sum(pps, u => u.liability),
                  prem: sum(pps, u => u.total_premium) };

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: "0 0 8px 34px", background: "#f7faf7" }}>
        <div style={{ ...uiLabel, padding: "8px 10px 4px" }}>
          PLANTED UNITS AS INSURED · {planted.length}
        </div>
        <table style={{ borderCollapse: "collapse", minWidth: 520 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>Unit</th>
              <th style={th}>Acres</th>
              <th style={th}>APH</th>
              <th style={th} title={"RMA's OWN per-acre guarantee for this unit — P11 field 120, Acre Guarantee Quantity — "
                                  + "with the prevent-plant and late-planting adjustments ALREADY applied by RMA. "
                                  + "NAU sends it; Rain & Hail truncates its P11 at 93 fields and sends none of it."}>
                Guar/ac <span style={{ fontWeight: 500, opacity: 0.65 }}>RMA</span>
              </th>
              <th style={th}>Liability</th>
              <th style={th}>Premium</th>
            </tr>
          </thead>
          <tbody>
            {planted.map((u, i) => (
              <tr key={i}>
                <td style={{ ...td, textAlign: "left", fontWeight: 700 }}>
                  {[u.unit_number, u.sub_unit].filter(Boolean).join("-") || "—"}
                  {u.unit_structure && <span style={{ color: T.faint, fontWeight: 500, fontSize: 10.5 }}> {u.unit_structure}</span>}
                </td>
                <td style={td}>{u.acres != null ? Number(u.acres).toFixed(1) : "—"}</td>
                <td style={td}>
                  {u.aph_yield != null ? Number(u.aph_yield).toFixed(Number(u.aph_yield) < 20 ? 1 : 0) : "—"}
                  {u.aph_yield != null && yieldUnit && (
                    <span style={{ color: T.faint, fontSize: 10 }}> {yieldUnit}</span>
                  )}
                </td>
                <td style={td} title={u.acre_guarantee_qty == null
                      ? "this AIP does not send RMA's stated guarantee"
                      : "RMA states " + Number(u.acre_guarantee_qty).toFixed(2) + " per acre"
                        + (u.acreage_status ? " after guarantee adjustment '" + u.acreage_status + "'" : "")}>
                  {u.acre_guarantee_qty != null
                    ? Number(u.acre_guarantee_qty).toFixed(1)
                    : <span style={{ color: T.faint }}>—</span>}
                  {u.acre_guarantee_qty != null && yieldUnit && (
                    <span style={{ color: T.faint, fontSize: 10 }}> {yieldUnit}</span>
                  )}
                  {/* The adjustment type is WHY a unit's guarantee sits below a full
                      one — L slides 1%/day, M caps that slide, P and E are prevent
                      plant. Printed raw beside the number so a REDUCED GUARANTEE is
                      never mistaken for a bad APH, which is the read this repo has
                      been caught making before. */}
                  {u.acreage_status && (
                    <span style={{ marginLeft: 4, fontSize: 10.5, fontWeight: 800, color: "#8a5e10" }}
                          title="RMA guarantee adjustment type (P11 field 38): L late planted, M maximum late-planted reduction, P prevented planting, E company-verified PP acres from another unit">
                      {u.acreage_status}
                    </span>
                  )}
                </td>
                <td style={td}>{u.liability != null ? fmtD0(u.liability) : "—"}</td>
                <td style={{ ...td, color: T.body }}>{u.total_premium != null ? fmtD0(u.total_premium) : "—"}</td>
              </tr>
            ))}
            <tr style={{ borderTop: `1px solid ${T.line}`, fontWeight: 800 }}>
              <td style={{ ...td, textAlign: "left" }}>Planted total</td>
              <td style={td}>{tot.acres > 0 ? tot.acres.toFixed(1) : "—"}</td>
              <td style={{ ...td, color: T.faint, fontWeight: 500, fontSize: 10.5 }}>
                {weightedAph(planted) != null ? `${weightedAph(planted)} wtd` : ""}
              </td>
              {/* TOTAL GUARANTEE IN CROP UNITS — Σ(RMA's stated per-acre guarantee ×
                  that unit's acres). Only the units RMA actually states one for are
                  counted, and the count is printed beside it when that is not all of
                  them, because a total summed over 4 of 9 units silently reads as the
                  whole cell's guarantee. Same rule as the grain tool's incomplete[]:
                  a dropped line makes the guarantee read SHORT, so it can never be
                  silent. */}
              <td style={{ ...td, fontWeight: 800 }}
                  title="Sum over the units RMA states a guarantee for: (per-acre guarantee x that unit's acres).">
                {(() => {
                  const withQty = planted.filter(u => u.acre_guarantee_qty != null && Number(u.acres) > 0);
                  if (!withQty.length) return <span style={{ color: T.faint, fontWeight: 500 }}>—</span>;
                  const qty = withQty.reduce((a, u) => a + Number(u.acre_guarantee_qty) * Number(u.acres), 0);
                  return (
                    <>
                      {Math.round(qty).toLocaleString()}
                      {withQty.length < planted.length && (
                        <span style={{ color: "#8a5e10", fontWeight: 700, fontSize: 10 }}
                              title={`RMA states a guarantee for ${withQty.length} of ${planted.length} planted units — this total covers only those.`}>
                          {" "}({withQty.length}/{planted.length})
                        </span>
                      )}
                    </>
                  );
                })()}
              </td>
              <td style={td}>{tot.liab > 0 ? fmtD0(tot.liab) : "—"}</td>
              <td style={td}>{tot.prem > 0 ? fmtD0(tot.prem) : "—"}</td>
            </tr>
            {/* PP is NOT part of the planted guarantee — one muted line so the acres
                aren't invisible, with the detail on the Prevent Plant tab. */}
            {pps.length > 0 && (
              <tr style={{ color: "#8a5e10", background: "#fbf6ea" }}>
                <td style={{ ...td, textAlign: "left", fontWeight: 700, color: "#8a5e10" }}
                    title="Prevent-plant units carry the reduced PP guarantee, not the full one — excluded from the planted totals above. See the Prevent Plant tab.">
                  + prevent plant · {pps.length} unit{pps.length === 1 ? "" : "s"}
                </td>
                <td style={{ ...td, color: "#8a5e10" }}>{ppTot.acres > 0 ? ppTot.acres.toFixed(1) : "—"}</td>
                <td style={td} />
                {/* PP units DO carry an RMA guarantee — the reduced one — but it is
                    not part of the planted guarantee this table totals, so the cell
                    stays blank here and the detail lives on the Prevent Plant tab. */}
                <td style={td} />
                <td style={{ ...td, color: "#8a5e10" }}>{ppTot.liab > 0 ? fmtD0(ppTot.liab) : "—"}</td>
                <td style={{ ...td, color: "#8a5e10" }}>{ppTot.prem > 0 ? fmtD0(ppTot.prem) : "—"}</td>
              </tr>
            )}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

// Loss code labels for P20A disbursement records
const LOSS_CODE_LABELS = { V:"Verified Loss", L:"Loss", Q:"Quality Adjustment", D:"Unfunded Escrow", E:"Escrow Funded" };

// Minimum yield thresholds — below these are loss adjustment artifacts, not real production
// Used to filter out prevented plant / catastrophic entries from yield averages
const MIN_YIELD_FLOOR = { CORN: 20, SOYBEANS: 8, WHEAT: 10 };
// Max per-record acreage — anything above this is a data error at the AIP/RMA level
// No single farmer has 10K+ acres of one crop in one county in our book
const MAX_ACRES_CAP = 5500;
function isValidYield(crop, ypa) {
  const floor = MIN_YIELD_FLOOR[(crop||'').toUpperCase()] || 5;
  return Number(ypa || 0) >= floor;
}
function isValidAcres(acres) {
  return Number(acres || 0) > 0 && Number(acres || 0) <= MAX_ACRES_CAP;
}
function isValidProdRow(r) {
  return isValidYield(r.crop, r.yield_per_acre) && isValidAcres(r.acres) && Number(r.production || 0) > 0;
}

// ─── PP Dedup ──────────────────────────────────────────────────────────────
// Remove generic PP rows (type "ALL" or empty practice) when more specific
// rows exist for the same county+crop. This handles phantom data from stale
// PDF imports that coexist with authoritative M13 data.
// "Generic" = a PP row that has no specific type/practice. NAU + R&H sometimes
// emit pp_eligibility entries with type="ALL" / practice="No Practice" for
// crops where a single PP coverage spans every type (potatoes are the common
// case — TBL is the only potato type we write but PP is reported as ALL).
// These shouldn't be displayed alongside the specific crop_coverage row, and
// when looking up APH / PP guarantee they should match the specific row by
// county+crop alone.
function isGenericPPRow(typeStr, practiceStr) {
  const typeUp  = (typeStr||"").toUpperCase();
  const practUp = (practiceStr||"").toUpperCase();
  return !typeUp || typeUp === "ALL"
      || !practUp || practUp === "NO PRACTICE SPECIFIED" || practUp === "NO PRACTICE";
}

function dedupPPRows(ppRows) {
  if (!ppRows || ppRows.length === 0) return [];
  return ppRows.filter(pp => {
    const countyUp = (pp.county||"").toUpperCase().trim();
    const cropUp   = (pp.crop||"").toUpperCase();
    if (!isGenericPPRow(pp.type, pp.practice)) return true; // keep specific rows always
    // Check if a more specific row exists for same county+crop
    const hasSpecific = ppRows.some(other => {
      if (other === pp) return false;
      const oCounty = (other.county||"").toUpperCase().trim();
      const oCrop   = (other.crop||"").toUpperCase();
      return oCounty === countyUp && oCrop === cropUp && !isGenericPPRow(other.type, other.practice);
    });
    return !hasSpecific; // drop generic only if a specific row exists
  });
}

// ─── RMA Official Prices ───────────────────────────────────────────────────
// Spring projected prices: corn/soy = Feb CBOT Dec/Nov futures avg
// Wheat = fall projected (set ~Sept via CBOT July SRW futures)
// Harvest prices: corn/soy = Oct CBOT avg; wheat = July CBOT SRW avg
// Effective guarantee = max(projected, harvest) — always use higher
// Sources: USDA RMA PM bulletins, confirmed March 2026
// ─────────────────────────────────────────────────────────────────────────
// Spring/Fall projected prices, $/unit. Default unit is $/bu unless listed in
// SPRING_PRICE_UNIT below (potatoes & cabbage = $/cwt, processing beans = $/ton).
// VERIFY annotations mark values that should be confirmed against the
// year's RMA Special Provisions bulletin or NY actuarial document — they're
// reasonable carry-forwards from the prior year, not authoritative.
const RMA_SPRING_PRICES = {
  // Corn GSG — Feb discovery, Dec CBOT
  2026: {
    CORN: 4.62, SOYBEANS: 11.09, WHEAT: 5.76, BARLEY: 5.45,
    // OATS CORRECTED 2026-08-11, 4.08 -> 3.30. Three independent sources agree on
    // 3.30 and none supports 4.08: Bay Farms' SOI prints "$3.30" on BOTH its
    // Ontario and Yates oats lines at a 100% price election, RMA's own ADM feed
    // carries 3.30 for commodity 0016 type 017 conventional, and the liabilities
    // of every full-election oat coverage in the book solve to 3.30 to the cent.
    // The feed already wins rung 1 of resolveRmaPrice() and 3.30/4.08 = 0.81 sits
    // inside rmaFeedAgrees()'s 0.4x-2.5x band, so the live guarantee was right —
    // this was the FALLBACK, i.e. exactly the number a failed pull falls back to.
    // TRITICALE CORRECTED 2026-08-12, 5.76 -> 3.51. It had been carrying WHEAT's
    // number, which is what 5.76 is — the same shape of error as the oats 4.08.
    // Triticale is not futures-discovered: RMA publishes a FIXED election for it
    // (plan 90) in the ADM, $3.51 for 2026 and $3.71 for 2027, identical across
    // all NY counties and every practice. Both triticale units in the book agree —
    // one solves to $3.5091, the other to $1.9305, which is that price at CAT's
    // 55%. As with oats, the live number was already right because the feed wins
    // rung 1; this was the fallback a failed pull would have dropped to.
    "GRAIN SORGHUM": 4.61, OATS: 3.30, TRITICALE: 3.51,
    POTATOES: 17.85,
    // Cabbage default is Green/Red Fresh Conventional ($24.55/cwt). For
    // processing-cabbage growers ($4.30/cwt) or organic ($52.54/$9.20),
    // the per-row price comes from CABBAGE_PRICES below via getCropPriceAndUnit.
    CABBAGE: 24.55,           // RMA 2026 NY Special Provisions
    "SWEET CORN": 130.00,     // VERIFY: NY sweet corn processing — $/ton
    "PROCESSING BEANS": 365.00, // VERIFY: NY snap beans contract — $/ton
  },
  // WHEAT 2023-2025 CORRECTED 2026-08-07 against RMA's own ADM (A00810, NY state 36,
  // commodity 0011, type 011 winter, practice 003 conventional, plan 02) — read straight
  // out of each year's ADM_YTD zip. The previous values (2023 8.70 / 2024 6.59 / 2025 5.56)
  // were from some other series and were wrong in all three years; corn and soybeans
  // reproduce the ADM to the cent in every year, wheat was the only crop that didn't.
  // Same read confirms the 2026 wheat 5.76 already here. Cross-checks against RMA's SCO/ECO
  // county revenue (sco_eco_county = expected_revenue / expected_yield) to the cent.
  // Historical wheat carries its SOURCE, and it is the same document the seed table
  // (DEFAULT_PRICES_BY_YEAR) cites — two copies of a price must be traceable to ONE
  // record, or the next reader cannot tell which drifted.
  2025: { CORN: 4.70, SOYBEANS: 10.54, WHEAT: 6.06, POTATOES: 10.00, CABBAGE: 24.55 },   // WHEAT: RMA PM-24-060 (NY winter wheat, projected)
  2024: { CORN: 4.66, SOYBEANS: 11.55, WHEAT: 6.72 },                                    // WHEAT: RMA PM-23-054 attachment, NY winter, 9/30/2023 SCD
  2023: { CORN: 5.91, SOYBEANS: 13.76, WHEAT: 8.45 },                                    // WHEAT: RMA PM-22-056 (NY winter wheat, projected)
};

// Corn final planting dates moved to src/miYieldModel.js (the model reads NY's);
// getCornFPD is imported at the top. Soy dates stay here.
// PA northern-tier counties (above ~I-80) — earlier soy FPD.
const PA_NORTHERN_COUNTIES = new Set([
  "ERIE","CRAWFORD","WARREN","MCKEAN","POTTER","TIOGA","BRADFORD","SUSQUEHANNA",
  "WAYNE","FOREST","ELK","CAMERON","CLINTON","LYCOMING","SULLIVAN","WYOMING",
  "LACKAWANNA","PIKE","MERCER","VENANGO","CLARION","JEFFERSON","CLEARFIELD",
]);
const SOY_FPD_BY_STATE = {
  NY: "June 10",        // uniform per RMA map
  PA: "June 20",        // default; northern-tier counties override to June 10
  OH: "June 20",        // approximate — verify
  MI: "June 20",        // approximate — verify
  IN: "June 20",        // approximate — verify
  MD: "June 20",
};
function getSoyFPD(state, county) {
  const st = (state||"").toUpperCase();
  if (st === "PA" && PA_NORTHERN_COUNTIES.has((county||"").toUpperCase().trim())) return "June 10";
  return SOY_FPD_BY_STATE[st] || null;
}

// Cabbage RMA prices vary by 4 dimensions: type (Green/Red — same price), market
// (Fresh / Processing), and production practice (Conventional / Organic).
// Source: 2026 NY RMA Special Provisions per Kevin 2026-04-28.
// Lookup key: `${market}_${practice}` — all in $/cwt.
const CABBAGE_PRICES = {
  2026: {
    FRESH_CONVENTIONAL: 24.55,
    FRESH_ORGANIC:      52.54,
    PROC_CONVENTIONAL:   4.30,
    PROC_ORGANIC:        9.20,
  },
  2025: {
    FRESH_CONVENTIONAL: 24.55,
    FRESH_ORGANIC:      52.54,
    PROC_CONVENTIONAL:   4.30,
    PROC_ORGANIC:        9.20,
  },
};
const RMA_HARVEST_PRICES = {
  // Corn/Soy = Oct discovery; Wheat + Barley = July discovery (NY winter).
  // null = published later this crop year.
  //
  // 2026 wheat + barley are the NEW YORK / WINTER / CONVENTIONAL rows of RMA
  // bulletin PM-26-043, "Prices for the July 1, 2026 – July 31, 2026 Discovery
  // Period" (published 2026-08-03). Wheat $6.52 lands one cent above our own
  // 21-day futures estimate of $6.5098 — see the missing 2026-07-30 bar noted
  // on HARVEST_DISCOVERY. Take the CONVENTIONAL column; the second column in
  // that attachment is ORGANIC (wheat $12.64, barley $6.64).
  2026: { CORN: null, SOYBEANS: null, WHEAT: 6.52, BARLEY: 4.18 },    // corn/soy set Oct 2026
  // WHEAT 2023-2025 CORRECTED 2026-08-07 from RMA's ADM (A00810 f35, NY/011/003/plan 02) —
  // was 2023 7.16 / 2024 6.80 / 2025 5.70, wrong in all three. See RMA_SPRING_PRICES above.
  2025: { CORN: 4.22, SOYBEANS: 10.35, WHEAT: 5.43 },
  2024: { CORN: 4.16, SOYBEANS: 10.03, WHEAT: 5.51 },
  2023: { CORN: 4.88, SOYBEANS: 12.84, WHEAT: 6.83 },
};
// Which month's discovery window sets each crop's harvest price, for the
// "Pending …" label on the prices page. NY winter WHEAT and BARLEY both run the
// July window (one RMA bulletin covers them); corn and soybeans run October.
const HARVEST_MONTH_LABEL = { WHEAT: "July", BARLEY: "July", CORN: "Oct", SOYBEANS: "Oct" };

// Per-crop pricing unit on the prices page. $/bu is the default; only override
// crops whose RMA election is published in something else.
// ONE crop order for the Agency Dashboard, shared by the historical and live
// years so a crop sits in the same position whichever year you're looking at.
// Each branch used to sort with its own list and drop unlisted crops into an
// arbitrary tail, so Grapes was 14th in 2025 and 6th in 2026 — the boxes moved
// under you when you switched years. Anything not listed sorts alphabetically
// after the listed ones rather than in Object.keys order.
const DASH_CROP_ORDER = [
  "CORN", "SOYBEANS", "WHEAT", "DRY BEANS", "POTATOES", "GRAPES", "ONIONS",
  "CABBAGE", "PROCESSING BEANS", "BARLEY", "TRITICALE", "OATS", "GRAIN SORGHUM",
  "APPLES", "PEACHES", "PRF", "FEEDER CATTLE", "FED CATTLE", "LRP", "DRP",
];
// The two branches name the same thing differently — historical_summaries files
// PRF as PASTURE,RANGELAND,FORAGE and DRP as MILK — so normalize before ranking.
function dashCropRank(crop) {
  const k = String(crop || "").toUpperCase().trim();
  const norm = (k === "PASTURE,RANGELAND,FORAGE" || k === "PASTURE, RANGELAND, FORAGE") ? "PRF"
             : k === "MILK" ? "DRP"
             : k === "PROC BEANS" ? "PROCESSING BEANS"
             : k;
  const i = DASH_CROP_ORDER.indexOf(norm);
  return i === -1 ? 999 : i;
}

const SPRING_PRICE_UNIT = {
  POTATOES: "cwt",
  CABBAGE: "cwt",
  "PROCESSING BEANS": "ton",
  "SWEET CORN": "ton",
};
// ── RMA price feed ──────────────────────────────────────────────────────────
// `/api/market?fn=rma-price` reads RMA's Actuarial Data Master daily and fills
// `rma_prices` (per crop year × commodity × state × county × type × practice).
// This is the automatic path: the day RMA publishes, the number changes here
// with nobody editing code.
//
// The hardcoded tables above are NOT dead. They are the verified fallback and
// the cross-check — a crop with no feed row keeps its constant, and a feed value
// that disagrees wildly with the constant is REFUSED and flagged rather than
// adopted. That guard is the whole reason it's safe to let a scraped file drive
// a revenue guarantee: the failure mode of a wrong ADM commodity code is a
// visible warning on the prices page, not a silently wrong guarantee.
//
// Module-level rather than React state because effectivePrice() is a plain
// function called from dozens of render paths that have no access to context.
let _rmaFeed = {};        // [year][CROP] = { projected, harvest, released, volatility, plan }
let _rmaFeedMeta = { loadedAt: null, rows: 0 };

// Which ADM (commodity, type) is "the" price for each crop we display. Corn is
// two products under one commodity code — 016 grain in $/bu and 026 silage in
// $/ton — so they get separate rows; blending them is the Lismore mistake in
// price form. type null = the crop has one type and we take whatever comes.
const RMA_ADM_CROPS = {
  CORN:               { code: "0041", type: "016" },
  "CORN SILAGE":      { code: "0041", type: "026" },
  SOYBEANS:           { code: "0081", type: "997" },
  WHEAT:              { code: "0011", type: "011" },
  BARLEY:             { code: "0091", type: null },
  OATS:               { code: "0016", type: null },
  TRITICALE:          { code: "0158", type: null },
  "GRAIN SORGHUM":    { code: "0051", type: null },
  POTATOES:           { code: "0084", type: null },
  CABBAGE:            { code: "0072", type: null },
  "PROCESSING BEANS": { code: "0046", type: null },
  "DRY BEANS":        { code: "0047", type: null },
};
// CERTIFIED organic carries its own (much higher) election — NY wheat reads $5.76
// conventional against $11.16 organic on the same CY2026 row set. It is kept APART under
// "<CROP>_ORGANIC" (WHEAT_ORGANIC), so it can never fold into the conventional price and the
// ladder can still confirm an organic figure against RMA's own row. TRANSITIONAL organic
// (714 / 712) is not in this set: RMA prices it at the conventional election ($5.76 on every
// CY2026 wheat row in NY/PA/OH/MD), and it stays in the conventional rollup.
const RMA_ORGANIC_PRACTICES = new Set(["702", "713"]);

// A feed price is adopted only if it is a positive finite number AND, when we
// already hold a verified constant, lands within a sane multiple of it. 0.4×–2.5×
// is wide enough for a real market move (2022 wheat ran +60% year over year) and
// tight enough to catch a wrong commodity code, a $/ton price landing on a $/bu
// row, or a units shift in the ADM.
// ⚠ TWO CHECKS, TWO QUESTIONS. The wide band sanity-checks a feed value against a
// verified CONSTANT (a wrong commodity code, a $/ton price on a $/bu row). The other
// is not a band at all: an ADM row and OUR OWN settled discovery average of the same
// window are two representations of ONE approved price, so the question is not
// whether they are close enough — it is whether they resolve to the same published
// cent.
//
// MEASURED (2026-09-15): of 5,000 published projected prices in rma_prices, ZERO sit
// off a whole cent, and the three CY2026 pairs where we hold both a published price
// and the bars each round to it exactly — WHEAT $5.7592 -> $5.76, SOYBEANS $11.0875
// -> $11.09, CORN $4.6158 -> $4.62.
//
// Both earlier rules looked reasonable. A first cut used 2%, which at $7.54 is 15c
// and would have called an ADM row of $7.65 agreement. A second used a $0.005
// difference, which is this rule everywhere except an exact half-cent tie — where
// floating point, not the business rule, decided the answer.
//
// The cent rule is rmaPriceCents(), SHARED with the CY2027 wheat quoter rather than
// re-typed. ⚠ Its tie direction (a half cent up) is inherited from the quoter, not
// sourced from RMA; a tie RMA rounded the other way reads as a CONFLICT, which
// refuses the dependent guarantee and asks a human — the safe direction.
function rmaSameCent(a, b) {
  const ca = rmaPriceCents(a), cb = rmaPriceCents(b);
  return ca != null && cb != null && ca === cb;
}
const RMA_FEED_BAND = { lo: 0.4, hi: 2.5 };
function rmaFeedAgrees(feedVal, knownVal) {
  if (!(Number.isFinite(feedVal) && feedVal > 0)) return false;
  if (!(Number.isFinite(knownVal) && knownVal > 0)) return true;   // nothing to check against
  const r = feedVal / knownVal;
  return r >= RMA_FEED_BAND.lo && r <= RMA_FEED_BAND.hi;
}

// Reduce raw rma_prices rows to one conventional price per (year, crop).
//
// RMA PRICES ARE PER STATE and the ladder has no state parameter — it answers ONE
// number per (year, crop), which is New York's, matching RMA_SPRING_PRICES. They are
// not always the same across the book's footprint: 2026 soybeans are $11.09 in NY and
// PA but $11.19 in MARYLAND, and corn silage is $38.25 in NY/PA/MD against $36.00 in
// OHIO. Nothing here reads state, so admitting another state's rows would let the last
// row win and silently move a price by a dime — inside the sanity band, so the guard
// would never see it. The hydration query filters state 36; this enforces the same
// thing at the reducer so a future caller can't quietly break it.
const RMA_FEED_STATE = "36";   // New York — the state the verified constants describe
// ── ⚠ THE COLLAPSED FEED CANNOT ANSWER FOR A CROP RMA PRICES PER PRACTICE ────
//
// `_rmaFeed` is keyed [year][CROP] (plus a _ORGANIC twin), so where the ADM
// publishes several prices for one crop the LAST row wins. Measured on the live
// CY2026 NY feed, six buckets disagree with themselves:
//
//     POTATOES            $17.85 x36   $33.60 x14
//     CABBAGE             $4.30 x28  $9.20 x14  $24.55 x28  $52.54 x14
//     DRY BEANS           $0.28 / $0.29 / $0.31 / $0.38 / $0.44
//     PROCESSING BEANS    $205 x352   $645 x24
//     ...and the _ORGANIC twins of the last two.
//
// Potatoes is the one that reached a customer. MARQUART's prevent-plant potato
// units are practice 997 / type 000, which the ADM prices at $17.85 — and the
// carrier's own stated liability back-solves to exactly $17.85 — while the
// collapsed bucket had taken $33.60 off a practice-723 row. 33.60/17.85 = 1.882,
// which is precisely the ratio AARON H MARQUART and THOMAS ADAM MARQUART read at
// on the claim cross-check. `rmaFeedAgrees` cannot catch it: 1.88x sits inside
// the 0.4-2.5x band, which is sized to admit a real market move.
//
// ⚠ AND THE FIX DELIBERATELY DOES NOT INTERPRET PRACTICE CODES. It would be easy
// to decide 723 is "organic certified" from the pattern (702/713/723 elevated,
// 712/714/724 base) and add it to RMA_ORGANIC_PRACTICES — but that is an
// inference about RMA's code list, the list is not in this repo, and a websearch
// did not surface it. Keying on the practice the POLICY states needs no such
// interpretation: it asks the ADM the same question the AIP asked.
//
// This map is ADDITIVE. `_rmaFeed` is untouched, so every existing caller and
// every guarantee in the app resolves exactly as before; only a caller that
// KNOWS its practice can reach the precise answer.
let _rmaFeedByPractice = {};    // [year][CROP|practice|type] = { projected, harvest }
function rmaFeedPriceFor(crop, year, kind, practiceCode, typeCode) {
  const y = _rmaFeedByPractice[Number(year)];
  if (!y || !crop || practiceCode == null) return null;
  const k = String(crop).toUpperCase() + "|" + String(practiceCode) + "|" + String(typeCode == null ? "" : typeCode);
  const hit = y[k];
  const v = hit ? hit[kind] : null;
  return Number.isFinite(v) && v > 0 ? v : null;
}

function buildRmaFeed(rows) {
  const byCropCode = {};
  for (const crop in RMA_ADM_CROPS) {
    const { code, type } = RMA_ADM_CROPS[crop];
    (byCropCode[code] = byCropCode[code] || []).push({ crop, type });
  }
  const out = {};
  const byPrac = {};
  for (const r of rows || []) {
    if (r.state_code != null && String(r.state_code) !== RMA_FEED_STATE) continue;
    const organic = RMA_ORGANIC_PRACTICES.has(String(r.practice_code || ""));
    const targets = byCropCode[String(r.commodity_code || "")];
    if (!targets) continue;
    for (const t of targets) {
      if (t.type && String(r.type_code || "") !== t.type) continue;
      // The precise map, alongside the collapsed one. Keyed on the practice and
      // type the ADM row states, so a caller holding the policy's own practice
      // gets the price the AIP rated on rather than whichever row landed last.
      {
        const yy = Number(r.crop_year);
        const pk = t.crop.toUpperCase() + "|" + String(r.practice_code || "") + "|" + String(r.type_code || "");
        const pb = (byPrac[yy] = byPrac[yy] || {});
        const pp = Number(r.projected_price), ph = Number(r.harvest_price);
        const pc = pb[pk];
        // Same plan-02 preference the collapsed map uses, so the two cannot
        // disagree about which PLAN answered for a crop that carries both.
        if (!(pc && pc.plan === "02" && r.plan_code !== "02")) {
          pb[pk] = {
            projected: Number.isFinite(pp) && pp > 0 ? pp : (pc ? pc.projected : null),
            harvest:   Number.isFinite(ph) && ph > 0 ? ph : (pc ? pc.harvest : null),
            plan: r.plan_code || (pc ? pc.plan : null),
          };
        }
      }
      const y = Number(r.crop_year);
      const bucket = (out[y] = out[y] || {});
      const proj = Number(r.projected_price), harv = Number(r.harvest_price);
      const key = organic ? t.crop + "_ORGANIC" : t.crop;
      const cur = bucket[key];
      // Prefer plan 02 (Revenue Protection, futures-discovered) over a plan-90
      // election when a crop carries both.
      if (cur && cur.plan === "02" && r.plan_code !== "02") continue;
      bucket[key] = {
        projected: Number.isFinite(proj) && proj > 0 ? proj : (cur ? cur.projected : null),
        harvest:   Number.isFinite(harv) && harv > 0 ? harv : (cur ? cur.harvest : null),
        volatility: r.volatility != null ? Number(r.volatility) : (cur ? cur.volatility : null),
        released:  r.released_date || (cur ? cur.released : null),
        plan:      r.plan_code || (cur ? cur.plan : null),
      };
    }
  }
  // Handed back beside the collapsed map rather than returned, so the function's
  // existing contract (and every caller of it) is unchanged.
  buildRmaFeed.__byPractice = byPrac;
  return out;
}
function setRmaPriceFeed(rows) {
  _rmaFeed = buildRmaFeed(rows);
  _rmaFeedByPractice = buildRmaFeed.__byPractice || {};
  _rmaFeedMeta = { loadedAt: new Date(), rows: (rows || []).length };
}
const rmaFeedFor = (crop, year) => (_rmaFeed[year] || {})[crop] || null;

// ── THE SETTLED PROJECTED-PRICE DISCOVERY AVERAGE ─────────────────────────
// RMA sets a crop's projected price as the simple average of one contract's
// settlements across a published window. When that window CLOSES the average can
// no longer move — it IS the number RMA will publish — but the ADM row does not
// appear for another week or two. Until it does, resolveRmaPrice() had no way to
// see it: rung 3 only ran when a CALLER passed bars, and effectivePrice() passes
// none. So CY2027 wheat read "pending" on every surface while the wheat quoter,
// which fetches WU27 itself, showed $7.54.
//
// This is the one authoritative path. Bars land here once, and every consumer
// reads them through the same ladder — no screen resolves its own price.
//
// ⚠ DELIBERATELY NARROW, because this is shared price plumbing:
//   • PROJECTED only. Harvest prices keep requiring explicit bars, so no harvest
//     figure can move (CY2026 wheat harvest is $6.52 from rung 2 regardless).
//   • Only (crop, year) pairs the loader found PENDING and CLOSED are ever put
//     in the store, so a price that already resolves cannot change.
//   • A caller that passes its own bars is untouched — same status, same source.
let _discBars = {};        // "WHEAT|2027" -> [{ bar_date, close }]
let _discMeta = { loadedAt: null, keys: [] };
function setDiscoveryBars(crop, year, bars) {
  _discBars[String(crop).toUpperCase() + "|" + Number(year)] = Array.isArray(bars) ? bars : [];
  _discMeta = { loadedAt: new Date(), keys: Object.keys(_discBars) };
}
function discoveryBarsFor(crop, year) {
  return _discBars[String(crop).toUpperCase() + "|" + Number(year)] || null;
}

// The ladder, in one place so every screen answers the same way:
//   1. RMA official, straight from the ADM        → "final", source "rma"
//   2. our verified hardcoded constant            → "final", source "book"
//   3. live futures estimate over the open window → "estimate"
//   4. nothing yet                                → "pending"
// `bars` is optional; without it the estimate rung is skipped and you get the
// first two rungs only, which is what non-market screens want.
function resolveRmaPriceFromSources(crop, year, kind /* "projected" | "harvest" */, bars) {
  const constant = kind === "harvest" ? RMA_HARVEST_PRICES[year]?.[crop] : RMA_SPRING_PRICES[year]?.[crop];
  // ⚠ A BULLETIN RECORD THAT SUPPLIES ITS PRICE stands exactly where a verified constant would:
  // RMA's own published figure, checked against the ADM row when one lands. Only CY2027
  // certified-organic wheat is such a record (PM-26-056) — there is no organic discovery
  // average for it. The conventional record is a citation and supplies nothing.
  const bulletin = rmaPriceApprovalFor(crop, year, kind);
  const fromBulletin = !(Number.isFinite(constant) && constant > 0) && !!(bulletin && bulletin.suppliesPrice);
  const known = fromBulletin ? bulletin.price : constant;
  const knownSource = fromBulletin ? "bulletin" : "book";
  const feed  = rmaFeedFor(crop, year);
  const feedVal = feed ? feed[kind] : null;

  // ⚠ COMPUTED BEFORE THE FEED BRANCH ON PURPOSE. Where we hold no verified
  // constant, our own SETTLED discovery average is the only thing an arriving ADM
  // row can be checked against — and without a check it would be adopted
  // unconditionally, because rmaFeedAgrees(v, null) returns true by design
  // ("nothing to check against"). That is the silent overwrite this ladder exists
  // to prevent, arriving by a new route.
  const storeBars = (!bars && kind === "projected") ? discoveryBarsFor(crop, year) : null;
  const est = (bars || storeBars) ? estimateDiscoveryFromBars(bars || storeBars, crop, year, kind) : null;
  const isSettled = !!(storeBars && est && est.closed);
  const hasKnown = Number.isFinite(known) && known > 0;
  // What a feed value is measured against: the verified constant if we have one
  // (unchanged behaviour), otherwise our settled discovery figure.
  const against = hasKnown ? known : (isSettled ? est.price : null);

  if (Number.isFinite(feedVal) && feedVal > 0) {
    // Two different comparisons, because they are asking different questions. The
    // CONSTANT check is a wide sanity band — it catches a wrong commodity code or a
    // $/ton price on a $/bu row. The DISCOVERY check asks whether the ADM row and our
    // own average of the same window resolve to the same published cent; anything
    // else is a disagreement worth a human.
    const agrees = hasKnown ? rmaFeedAgrees(feedVal, known)
                            : (against == null ? true : rmaSameCent(feedVal, against));
    if (agrees) {
      // PROVENANCE GRADUATES, THE ECONOMIC VALUE DOES NOT. The ADM row is the same
      // approved number arriving over the normal feed, so the price becomes
      // ADM-sourced without a figure moving on any screen.
      return { price: feedVal, status: "final", source: "rma", released: feed.released,
               volatility: feed.volatility, known: known ?? null,
               graduatedFrom: hasKnown ? null : (isSettled ? "discovery" : null) };
    }
    // REFUSED — never a silent swap. Keep what we had and carry the disagreement so
    // a screen can show it and a human can adjudicate.
    if (against != null) {
      return hasKnown
        ? { price: known, status: "final", source: knownSource, conflict: { feed: feedVal, [knownSource]: known },
            released: feed.released, known }
        : { price: against, status: "settled", source: "discovery",
            conflict: { feed: feedVal, discovery: against }, days: est.days, window: est.window,
            released: feed.released, known: null };
    }
  }
  if (hasKnown) return { price: known, status: "final", source: knownSource, known };

  if (est) {
    // ⚠ "settled" DESCRIBES OUR CONFIRMATION STATE, NOT RMA'S APPROVAL STATE.
    // RMA approves a projected price in its own bulletin (CY2027 wheat: PM-26-056,
    // published 2026-09-15) and the ADM/AIB/RIRS distribution that follows is
    // transport, not the event that makes it official. So a settled price is NOT a
    // draft and must never be presented as an estimate — it is the approved number,
    // arrived at independently, which this app has not yet confirmed against an RMA
    // source it can read. It graduates to source "rma" the moment the ADM row lands
    // and agrees. See the temporary provenance limitation in CLAUDE.md.
    return { ...est, status: isSettled ? "settled" : "estimate",
             source: isSettled ? "discovery" : "futures", known: null };
  }

  const win = discoveryWindow(crop, year, kind);
  return { price: null, status: "pending", source: null, window: win, known: null };
}

// ── RMA'S APPROVAL OF A RESOLVED PRICE — A CITATION, NEVER A SOURCE ───────
// Kevin, 2026-09-15: the price is FINAL — RMA approved it in PM-26-056 — while
// Silas's own automated chain has not yet confirmed it against an RMA source it
// reads. Those are two kinds of provenance and they are carried separately:
//   • economic authority — `approval`: who approved this number, and when;
//   • machine provenance — `status` / `source`: how Silas obtained it.
// The approval is ONE bounded record beside WHEAT_2027 in the wheat module. It is
// read only for the exact (crop, crop year, kind) it names, so it cannot appear on
// a CY2028 price, and it attaches only where the resolved price is the same
// published cent the record cites. It never supplies a price.
// A FUNCTION, not a const: the ladder above reads it, and a const declared below its first
// reader is a temporal-dead-zone crash waiting for anything that prices at module load.
function rmaPriceApprovals() {
  return [WHEAT_2027_PROJECTED_APPROVAL, WHEAT_2027_ORGANIC_PROJECTED];
}
// The ladder key a record describes: certified organic is priced under "<CROP>_ORGANIC".
function rmaApprovalKey(a) {
  return a.practice === "certified-organic" ? a.crop + "_ORGANIC" : a.crop;
}
function rmaPriceApprovalFor(crop, year, kind) {
  const c = String(crop).toUpperCase();
  return rmaPriceApprovals().find(a => a && rmaApprovalKey(a) === c && a.cropYear === Number(year) && a.kind === kind) || null;
}
function withRmaApproval(r, crop, year, kind) {
  const a = rmaPriceApprovalFor(crop, year, kind);
  if (!a || !r || r.price == null || r.conflict) return r;
  // A running average in an open window is not the number the citation is about.
  if (r.status !== "settled" && r.status !== "final") return r;
  if (rmaSameCent(r.price, a.price)) {
    return { ...r, approval: { bulletin: a.bulletin, approved: a.approved, price: a.price } };
  }
  // ⚠ The price we resolved is NOT the cent RMA approved. That is the same dispute as
  // an ADM row that disagrees and is handled the same way: the figure stays on screen
  // with its disagreement, and every calculation built on it refuses.
  return { ...r, conflict: { approved: a.price, [r.source === "rma" ? "feed" : r.source]: r.price } };
}
function resolveRmaPrice(crop, year, kind /* "projected" | "harvest" */, bars) {
  return withRmaApproval(resolveRmaPriceFromSources(crop, year, kind, bars), crop, year, kind);
}

// Effective guarantee uses max(projected, harvest). Harvest null = not yet available.
// Both sides run through the ladder, so an RMA-published price drives the real
// guarantee the day it lands — with the band guard above meaning a bad feed can
// only ever leave the verified number in place, never replace it with nonsense
// and never blank it.
// ⚠ A CONFLICTED PRICE STILL DISPLAYS, BUT MUST NOT CALCULATE. Kevin, on the
// graduation rule: "do not change the displayed economic value silently. Flag the
// conflict for review and refuse downstream calculations that depend on resolving
// that conflict until we know which value is correct." So the number stays on
// screen with its disagreement shown, and every guarantee built on it refuses.
function rmaPriceConflict(crop, year, kind) {
  const r = resolveRmaPrice(crop, year, kind || "projected");
  return r && r.conflict ? { ...r.conflict, price: r.price, status: r.status } : null;
}

// ── WHAT A READER IS TOLD ABOUT A RESOLVED PRICE ──────────────────────────
// Kevin: "don't expose that internal distinction as though the government price
// itself remains unofficial … When ADM arrives and agrees, the user should see no
// visual change whatsoever." So `label` and `detail` follow the ECONOMIC state
// (approved / published / discovery closed / tracking / disputed), and only
// `machine` — shown to an admin — says how Silas obtained the number. An approved
// price reads identically whether Silas holds it from discovery or from the ADM.
const RMA_CONFLICT_PARTY = { feed: "RMA ADM feed", discovery: "Silas discovery average",
                             book: "verified constant", bulletin: "RMA bulletin figure",
                             approved: "RMA-approved figure" };
const RMA_MONTHS = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.", "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."];
function rmaApprovalDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? `${RMA_MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : String(iso || "");
}
// ⚠ ONE SENTENCE FOR A PRICE DISPUTE, WHATEVER ITS PARTIES. A conflict used to be only
// { feed, book }, and two screens printed `c.book.toFixed(2)` directly. A disagreement
// with our settled discovery average or with the approved figure carries no `book`, so
// the RMA Prices page would have THROWN on the one day it is needed. Every consumer reads
// the parties through this.
function rmaConflictText(conflict) {
  return Object.entries(conflict || {})
    .filter(([k, v]) => RMA_CONFLICT_PARTY[k] && Number.isFinite(Number(v)))
    .map(([k, v]) => RMA_CONFLICT_PARTY[k] + " $" + (rmaPriceCents(v) / 100).toFixed(2))
    .join(" vs ");
}
function rmaPriceDisplay(r, kind) {
  const what = kind === "harvest" ? "harvest price" : "projected price";
  if (!r || r.price == null) {
    return { tone: "pending", label: "Not yet published",
             detail: r && r.window && r.window.label ? "RMA sets it from " + r.window.label : null, machine: null };
  }
  if (r.conflict) {
    return { tone: "conflict", label: "Under review — RMA figures disagree",
             detail: rmaConflictText(r.conflict) + " · guarantees built on this price are withheld",
             machine: "Silas source: " + (r.source || "unknown") + " · " + (r.status || "") };
  }
  if (r.approval) {
    return { tone: "final", label: "Final RMA " + what,
             detail: "RMA " + r.approval.bulletin + " · Approved " + rmaApprovalDate(r.approval.approved),
             machine: r.source === "rma"
               ? "Silas source: RMA ADM feed · matches the approved figure"
               : r.source === "bulletin"
               ? "Silas source: RMA bulletin figure · awaiting ADM confirmation"
               : "Silas source: completed discovery calculation · awaiting ADM confirmation" };
  }
  if (r.status === "final") {
    return { tone: "final", label: "Final RMA " + what,
             detail: r.source === "rma" ? "RMA Actuarial Data Master" + (r.released ? " · released " + r.released : "")
                                        : "RMA published price",
             machine: "Silas source: " + (r.source === "rma" ? "RMA ADM feed" : "verified constant") };
  }
  if (r.status === "settled") {
    return { tone: "settled", label: "Discovery closed — awaiting RMA publication",
             detail: (r.window && r.window.label ? r.window.label : "discovery window") + " · " + (r.days || 0) + " trading days",
             machine: "Silas source: completed discovery calculation · no RMA publication matched" };
  }
  return { tone: "estimate", label: "Tracking — discovery window open",
           detail: (r.window && r.window.label ? r.window.label : "discovery window") + " · " + (r.days || 0) + " trading days so far",
           machine: "Silas source: live futures average" };
}

function effectivePrice(crop, year) {
  const sp = resolveRmaPrice(crop, year, "projected").price;
  const hp = resolveRmaPrice(crop, year, "harvest").price;
  if (sp == null) return hp || 0;
  if (hp == null) return sp;
  return Math.max(sp, hp);
}

// ── Running "estimated harvest price" from futures (before RMA publishes final) ──
// RMA's harvest price = the simple average of the daily SETTLEMENTS of ONE futures
// contract across a discovery window, per crop:
//   • NY soft red winter WHEAT → CBOT September contract (WU‹yy›), averaged over JULY
//   • CORN                     → CBOT December  contract (CZ‹yy›), averaged over OCTOBER
//   • SOYBEANS                 → CBOT November  contract (SX‹yy›), averaged over OCTOBER
// Per-contract daily bars live in futures_contract_history (contract_sym like 'WU26',
// close = settlement). While the window is still open we average the days we already
// have → a live estimate that firms up each trading day; the moment RMA publishes the
// official number (hardcoded into RMA_HARVEST_PRICES[year][crop]) that value wins.
// The contract_sym prefix + month codes mirror api/_futures.js (MKT_PFX / MKT_MO).
const HARVEST_DISCOVERY = {
  WHEAT:    { commodity: 'wheat',    prefix: 'W', monthCode: 'U', discMonth: 7,  label: 'Sept CBOT wheat, avg of July' },
  CORN:     { commodity: 'corn',     prefix: 'C', monthCode: 'Z', discMonth: 10, label: 'Dec CBOT corn, avg of Oct' },
  SOYBEANS: { commodity: 'soybeans', prefix: 'S', monthCode: 'X', discMonth: 10, label: 'Nov CBOT soybeans, avg of Oct' },
};

// Both ends of the same idea. RMA sets the PROJECTED price on a window before
// the season and the HARVEST price on a window after it, off the SAME contract —
// so one table describes both, and the tracker can run on either. Windows are
// explicit date ranges rather than a month number because wheat's projected
// window (Aug 15 – Sep 14 of the PRIOR calendar year) crosses both a month and a
// year boundary; a month number cannot express it.
//
// Only crops with a real futures market appear here. Barley, oats, potatoes and
// the vegetables are published as fixed elections with no board to track, so they
// have no estimate rung — they go straight from "pending" to the RMA number.
const PRICE_DISCOVERY = {
  WHEAT: {
    commodity: 'wheat', prefix: 'W', monthCode: 'U',
    projected: (y) => ({ from: `${y - 1}-08-15`, to: `${y - 1}-09-14`, label: 'Sept CBOT wheat, avg of Aug 15 – Sep 14' }),
    harvest:   (y) => ({ from: `${y}-07-01`,     to: `${y}-07-31`,     label: 'Sept CBOT wheat, avg of July' }),
  },
  CORN: {
    commodity: 'corn', prefix: 'C', monthCode: 'Z',
    projected: (y) => ({ from: `${y}-02-01`, to: `${y}-02-29`, label: 'Dec CBOT corn, avg of February' }),
    harvest:   (y) => ({ from: `${y}-10-01`, to: `${y}-10-31`, label: 'Dec CBOT corn, avg of October' }),
  },
  SOYBEANS: {
    commodity: 'soybeans', prefix: 'S', monthCode: 'X',
    projected: (y) => ({ from: `${y}-02-01`, to: `${y}-02-29`, label: 'Nov CBOT soybeans, avg of February' }),
    harvest:   (y) => ({ from: `${y}-10-01`, to: `${y}-10-31`, label: 'Nov CBOT soybeans, avg of October' }),
  },
};
function discoveryContractSym(crop, year) {
  const cfg = PRICE_DISCOVERY[String(crop).toUpperCase()];
  return cfg ? `${cfg.prefix}${cfg.monthCode}${String(year).slice(-2)}` : null;
}
function discoveryWindow(crop, year, kind) {
  const cfg = PRICE_DISCOVERY[String(crop).toUpperCase()];
  if (!cfg || !cfg[kind]) return null;
  return { ...cfg[kind](Number(year)), sym: discoveryContractSym(crop, year), commodity: cfg.commodity };
}
// ── WHEN A CROP ROLLS TO THE NEXT CROP YEAR ───────────────────────────────
// Kevin, 2026-09-15: roll "as soon as we have new year price guarantees" — wheat
// the day discovery closes, corn and soybeans after February.
//
// ⚠ DERIVED, NOT TYPED. The projected-price discovery window is already expressed
// per crop as a FUNCTION OF THE YEAR in PRICE_DISCOVERY above, so the rollover
// date needs no constant and no per-year module: a crop has rolled to year N once
// N's projected window has closed, because that is the first instant the year can
// be quantified at all. A hardcoded 14 Sep / last-of-February is the guessed-day
// failure this file already records for crons, where three of four NASS schedules
// were misaligned and one was a full month late for its whole life.
//
// Verified against the shipped windows: WHEAT rolls to CY2027 on 2026-09-15 and to
// CY2028 on 2027-09-15; CORN and SOYBEANS hold CY2026 until 2027-03-01. Those are
// exactly the dates asked for, and nothing here names one.
//
// A crop with no futures board (barley, oats, the vegetables) has no window and
// returns null — it is published as a fixed election and cannot be tracked, so the
// caller must fall back rather than be handed a guess.
function cropRolloverYear(crop, todayISO) {
  const today = todayISO || new Date().toISOString().slice(0, 10);
  const base = Number(today.slice(0, 4));
  for (let y = base + 2; y >= base - 2; y--) {
    const w = discoveryWindow(crop, y, "projected");
    if (w && w.to && today > w.to) return y;
  }
  return null;
}

function harvestContractSym(crop, year) {
  const cfg = HARVEST_DISCOVERY[String(crop).toUpperCase()];
  return cfg ? `${cfg.prefix}${cfg.monthCode}${String(year).slice(-2)}` : null;
}
// Average the closes inside a discovery window. Also reports how many trading
// days the window SHOULD hold once closed vs how many we actually have — a gap
// there is the 2026-07-30 problem (a missed USDA report is unrecoverable and
// silently biased our wheat average by a cent), and it's better shown than not.
function estimateDiscoveryFromBars(bars, crop, year, kind) {
  const win = discoveryWindow(crop, year, kind);
  if (!win) return null;
  const rows = (bars || []).filter(b => b.bar_date >= win.from && b.bar_date <= win.to && b.close != null);
  if (!rows.length) return null;
  const sum = rows.reduce((s, b) => s + Number(b.close), 0);
  const lastDay = rows.reduce((a, b) => (b.bar_date > a ? b.bar_date : a), rows[0].bar_date);
  const today = new Date().toISOString().slice(0, 10);
  return {
    price: sum / rows.length, days: rows.length, lastDay,
    window: win, closed: today > win.to,
    expectedDays: businessDaysBetween(win.from, win.to > today ? today : win.to),
  };
}
// Weekday count, holidays not excluded — this is a "did we miss a day?" hint, so
// a small overcount around a market holiday is fine and a large gap is the signal.
function businessDaysBetween(fromISO, toISO) {
  let n = 0;
  const d = new Date(fromISO + "T00:00:00Z"), end = new Date(toISO + "T00:00:00Z");
  while (d <= end) { const w = d.getUTCDay(); if (w !== 0 && w !== 6) n++; d.setUTCDate(d.getUTCDate() + 1); }
  return n;
}
// From the per-contract daily bars, average the closes that fall inside the crop-year
// discovery month. Returns {price, days, lastDay} or null when no bars land in-window.
function estimateHarvestFromBars(bars, crop, year) {
  const cfg = HARVEST_DISCOVERY[String(crop).toUpperCase()];
  if (!cfg) return null;
  const mm = String(cfg.discMonth).padStart(2, '0');
  const lo = `${year}-${mm}-01`, hi = `${year}-${mm}-31`;
  const win = (bars || []).filter(b => b.bar_date >= lo && b.bar_date <= hi && b.close != null);
  if (!win.length) return null;
  const sum = win.reduce((s, b) => s + Number(b.close), 0);
  const lastDay = win.reduce((a, b) => (b.bar_date > a ? b.bar_date : a), win[0].bar_date);
  return { price: sum / win.length, days: win.length, lastDay };
}
// Single source of truth for the estimate→final switch. Returns the RMA FINAL harvest
// price once it's published (hardcoded into RMA_HARVEST_PRICES[year][crop]); otherwise
// the running futures estimate from the discovery-month bars. `isFinal` drives the
// EST/FINAL badge. Filling that one constant when RMA posts the official number flips
// every screen — detail AND the Claims Manager roster — from estimate to actual at once.
function resolveHarvestPrice(crop, year, bars) {
  const finalP = RMA_HARVEST_PRICES[year]?.[String(crop).toUpperCase()];
  if (finalP != null) return { price: finalP, isFinal: true };
  const est = estimateHarvestFromBars(bars, crop, year);
  return est ? { ...est, isFinal: false } : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// RMA SCO/ECO COUNTY EXPECTED REVENUE — 2025 NY + PA, corn + soybeans + wheat
//
// Source: RMA "SCO and ECO Yields, Revenues, and Payment Indicators" report,
// pulled 2026-05-14 from webapp.rma.usda.gov/apps/RIRS. Canonical row per
// (state, county, crop) is non-irrigated conventional (Practice 003,
// Organic 997) with the crop-appropriate Type Code: Corn=016 Grain,
// Wheat=011 Winter, Soybeans=997 (no type split).
//
// Wheat 2025 carries finalCountyYield because RMA already released the
// official county yields for winter wheat (harvested July 2025; finals
// published ~Feb-March 2026). Corn/Soy 2025 finals release ~June 2026 —
// the matrix uses our book yields as a proxy until then.
//
// Add years / crops by mirroring the {YEAR: {CROP: {STATE: {COUNTY: {…}}}}}
// shape. Keep COUNTY keys uppercase to match production_history.
//
// NB: wheat prices here ($6.06 projected / $5.43 harvest) are RMA's 2025 NY
// Winter Wheat values and differ from the hardcoded RMA_SPRING_PRICES[2025]
// .WHEAT ($5.56 / $5.70). The hardcoded values look like a different wheat
// class — separate cleanup, not blocking this feature.
// ─────────────────────────────────────────────────────────────────────────────
const RMA_COUNTY_EXPECTED_REVENUE = {
  2025: {
    CORN: {
      NY: {
        ALBANY:       { expectedYield: 152.6, finalCountyYield: 150.4, projectedPrice: 4.70, expectedRevenue: 717.22, harvestPrice: 4.22 },
        ALLEGANY:     { expectedYield: 150.8, finalCountyYield: 135.8, projectedPrice: 4.70, expectedRevenue: 708.76, harvestPrice: 4.22 },
        BROOME:       { expectedYield: 141.5, finalCountyYield: 105.3, projectedPrice: 4.70, expectedRevenue: 665.05, harvestPrice: 4.22 },
        CATTARAUGUS:  { expectedYield: 150.7, finalCountyYield: 141.2, projectedPrice: 4.70, expectedRevenue: 708.29, harvestPrice: 4.22 },
        CAYUGA:       { expectedYield: 157.4, finalCountyYield: 139.8, projectedPrice: 4.70, expectedRevenue: 739.78, harvestPrice: 4.22 },
        CHAUTAUQUA:   { expectedYield: 156.3, finalCountyYield: 144.9, projectedPrice: 4.70, expectedRevenue: 734.61, harvestPrice: 4.22 },
        CHEMUNG:      { expectedYield: 129.5, finalCountyYield: 121.6, projectedPrice: 4.70, expectedRevenue: 608.65, harvestPrice: 4.22 },
        CHENANGO:     { expectedYield: 140.1, finalCountyYield: 101.2, projectedPrice: 4.70, expectedRevenue: 658.47, harvestPrice: 4.22 },
        CLINTON:      { expectedYield: 142.0, finalCountyYield: 139.9, projectedPrice: 4.70, expectedRevenue: 667.40, harvestPrice: 4.22 },
        COLUMBIA:     { expectedYield: 158.5, finalCountyYield: 157.2, projectedPrice: 4.70, expectedRevenue: 744.95, harvestPrice: 4.22 },
        CORTLAND:     { expectedYield: 143.6, finalCountyYield: 130, projectedPrice: 4.70, expectedRevenue: 674.92, harvestPrice: 4.22 },
        DELAWARE:     { expectedYield: 138.4, finalCountyYield: 103.5, projectedPrice: 4.70, expectedRevenue: 650.48, harvestPrice: 4.22 },
        DUTCHESS:     { expectedYield: 156.9, finalCountyYield: 152.3, projectedPrice: 4.70, expectedRevenue: 737.43, harvestPrice: 4.22 },
        ERIE:         { expectedYield: 137.6, finalCountyYield: 119.7, projectedPrice: 4.70, expectedRevenue: 646.72, harvestPrice: 4.22 },
        ESSEX:        { expectedYield: 122.0, finalCountyYield: 108.7, projectedPrice: 4.70, expectedRevenue: 573.40, harvestPrice: 4.22 },
        FRANKLIN:     { expectedYield: 133.5, finalCountyYield: 134.4, projectedPrice: 4.70, expectedRevenue: 627.45, harvestPrice: 4.22 },
        FULTON:       { expectedYield: 134.8, finalCountyYield: 133.3, projectedPrice: 4.70, expectedRevenue: 633.56, harvestPrice: 4.22 },
        GENESEE:      { expectedYield: 161.3, finalCountyYield: 121, projectedPrice: 4.70, expectedRevenue: 758.11, harvestPrice: 4.22 },
        GREENE:       { expectedYield: 158.6, finalCountyYield: 147.1, projectedPrice: 4.70, expectedRevenue: 745.42, harvestPrice: 4.22 },
        HERKIMER:     { expectedYield: 136.8, finalCountyYield: 130.8, projectedPrice: 4.70, expectedRevenue: 642.96, harvestPrice: 4.22 },
        JEFFERSON:    { expectedYield: 132.1, finalCountyYield: 107, projectedPrice: 4.70, expectedRevenue: 620.87, harvestPrice: 4.22 },
        LEWIS:        { expectedYield: 129.9, finalCountyYield: 130.9, projectedPrice: 4.70, expectedRevenue: 610.53, harvestPrice: 4.22 },
        LIVINGSTON:   { expectedYield: 180.1, finalCountyYield: 157.1, projectedPrice: 4.70, expectedRevenue: 846.47, harvestPrice: 4.22 },
        MADISON:      { expectedYield: 145.0, finalCountyYield: 120.1, projectedPrice: 4.70, expectedRevenue: 681.50, harvestPrice: 4.22 },
        MONROE:       { expectedYield: 178.1, finalCountyYield: 154.4, projectedPrice: 4.70, expectedRevenue: 837.07, harvestPrice: 4.22 },
        MONTGOMERY:   { expectedYield: 136.8, finalCountyYield: 128.4, projectedPrice: 4.70, expectedRevenue: 642.96, harvestPrice: 4.22 },
        NIAGARA:      { expectedYield: 166.5, finalCountyYield: 136.6, projectedPrice: 4.70, expectedRevenue: 782.55, harvestPrice: 4.22 },
        ONEIDA:       { expectedYield: 158.5, finalCountyYield: 134.9, projectedPrice: 4.70, expectedRevenue: 744.95, harvestPrice: 4.22 },
        ONONDAGA:     { expectedYield: 151.5, finalCountyYield: 136.9, projectedPrice: 4.70, expectedRevenue: 712.05, harvestPrice: 4.22 },
        ONTARIO:      { expectedYield: 165.2, finalCountyYield: 146.6, projectedPrice: 4.70, expectedRevenue: 776.44, harvestPrice: 4.22 },
        ORANGE:       { expectedYield: 137.5, finalCountyYield: 121.1, projectedPrice: 4.70, expectedRevenue: 646.25, harvestPrice: 4.22 },
        ORLEANS:      { expectedYield: 182.5, finalCountyYield: 150.1, projectedPrice: 4.70, expectedRevenue: 857.75, harvestPrice: 4.22 },
        OSWEGO:       { expectedYield: 146.2, finalCountyYield: 123.9, projectedPrice: 4.70, expectedRevenue: 687.14, harvestPrice: 4.22 },
        OTSEGO:       { expectedYield: 152.4, finalCountyYield: 125.4, projectedPrice: 4.70, expectedRevenue: 716.28, harvestPrice: 4.22 },
        RENSSELAER:   { expectedYield: 141.3, finalCountyYield: 108.2, projectedPrice: 4.70, expectedRevenue: 664.11, harvestPrice: 4.22 },
        'ST LAWRENCE':{ expectedYield: 139.6, finalCountyYield: 117.4, projectedPrice: 4.70, expectedRevenue: 656.12, harvestPrice: 4.22 },
        SARATOGA:     { expectedYield: 137.8, finalCountyYield: 113.8, projectedPrice: 4.70, expectedRevenue: 647.66, harvestPrice: 4.22 },
        SCHENECTADY:  { expectedYield: 134.1, finalCountyYield: 128.6, projectedPrice: 4.70, expectedRevenue: 630.27, harvestPrice: 4.22 },
        SCHOHARIE:    { expectedYield: 149.2, finalCountyYield: 143.4, projectedPrice: 4.70, expectedRevenue: 701.24, harvestPrice: 4.22 },
        SCHUYLER:     { expectedYield: 167.5, finalCountyYield: 153.5, projectedPrice: 4.70, expectedRevenue: 787.25, harvestPrice: 4.22 },
        SENECA:       { expectedYield: 172.7, finalCountyYield: 151.3, projectedPrice: 4.70, expectedRevenue: 811.69, harvestPrice: 4.22 },
        STEUBEN:      { expectedYield: 152.4, finalCountyYield: 113.6, projectedPrice: 4.70, expectedRevenue: 716.28, harvestPrice: 4.22 },
        SUFFOLK:      { expectedYield: 151.9, projectedPrice: 4.70, expectedRevenue: 713.93, harvestPrice: 4.22 },
        SULLIVAN:     { expectedYield: 140.4, projectedPrice: 4.70, expectedRevenue: 659.88, harvestPrice: 4.22 },
        TIOGA:        { expectedYield: 135.0, finalCountyYield: 119.9, projectedPrice: 4.70, expectedRevenue: 634.50, harvestPrice: 4.22 },
        TOMPKINS:     { expectedYield: 167.9, finalCountyYield: 148.6, projectedPrice: 4.70, expectedRevenue: 789.13, harvestPrice: 4.22 },
        ULSTER:       { expectedYield: 157.7, finalCountyYield: 159, projectedPrice: 4.70, expectedRevenue: 741.19, harvestPrice: 4.22 },
        WARREN:       { expectedYield: 124.7, projectedPrice: 4.70, expectedRevenue: 586.09, harvestPrice: 4.22 },
        WASHINGTON:   { expectedYield: 124.3, finalCountyYield: 109.6, projectedPrice: 4.70, expectedRevenue: 584.21, harvestPrice: 4.22 },
        WAYNE:        { expectedYield: 172.2, finalCountyYield: 157.3, projectedPrice: 4.70, expectedRevenue: 809.34, harvestPrice: 4.22 },
        WYOMING:      { expectedYield: 159.6, finalCountyYield: 147.8, projectedPrice: 4.70, expectedRevenue: 750.12, harvestPrice: 4.22 },
        YATES:        { expectedYield: 174.5, finalCountyYield: 157.4, projectedPrice: 4.70, expectedRevenue: 820.15, harvestPrice: 4.22 },
      },
      PA: {
        ADAMS:        { expectedYield: 141.5, projectedPrice: 4.70, expectedRevenue: 665.05, harvestPrice: 4.22 },
        ALLEGHENY:    { expectedYield: 145.4, projectedPrice: 4.70, expectedRevenue: 683.38, harvestPrice: 4.22 },
        ARMSTRONG:    { expectedYield: 130.5, projectedPrice: 4.70, expectedRevenue: 613.35, harvestPrice: 4.22 },
        BEAVER:       { expectedYield: 153.7, projectedPrice: 4.70, expectedRevenue: 722.39, harvestPrice: 4.22 },
        BEDFORD:      { expectedYield: 141.8, projectedPrice: 4.70, expectedRevenue: 666.46, harvestPrice: 4.22 },
        BERKS:        { expectedYield: 169.7, projectedPrice: 4.70, expectedRevenue: 797.59, harvestPrice: 4.22 },
        BLAIR:        { expectedYield: 173.3, projectedPrice: 4.70, expectedRevenue: 814.51, harvestPrice: 4.22 },
        BRADFORD:     { expectedYield: 125.2, projectedPrice: 4.70, expectedRevenue: 588.44, harvestPrice: 4.22 },
        BUCKS:        { expectedYield: 141.9, projectedPrice: 4.70, expectedRevenue: 666.93, harvestPrice: 4.22 },
        BUTLER:       { expectedYield: 145.5, projectedPrice: 4.70, expectedRevenue: 683.85, harvestPrice: 4.22 },
        CAMBRIA:      { expectedYield: 146.9, projectedPrice: 4.70, expectedRevenue: 690.43, harvestPrice: 4.22 },
        CAMERON:      { expectedYield: 140.1, projectedPrice: 4.70, expectedRevenue: 658.47, harvestPrice: 4.22 },
        CARBON:       { expectedYield: 159.2, projectedPrice: 4.70, expectedRevenue: 748.24, harvestPrice: 4.22 },
        CENTRE:       { expectedYield: 160.2, projectedPrice: 4.70, expectedRevenue: 752.94, harvestPrice: 4.22 },
        CHESTER:      { expectedYield: 201.1, projectedPrice: 4.70, expectedRevenue: 945.17, harvestPrice: 4.22 },
        CLARION:      { expectedYield: 137.1, projectedPrice: 4.70, expectedRevenue: 644.37, harvestPrice: 4.22 },
        CLEARFIELD:   { expectedYield: 138.1, projectedPrice: 4.70, expectedRevenue: 649.07, harvestPrice: 4.22 },
        CLINTON:      { expectedYield: 153.1, projectedPrice: 4.70, expectedRevenue: 719.57, harvestPrice: 4.22 },
        COLUMBIA:     { expectedYield: 153.8, projectedPrice: 4.70, expectedRevenue: 722.86, harvestPrice: 4.22 },
        CRAWFORD:     { expectedYield: 149.2, projectedPrice: 4.70, expectedRevenue: 701.24, harvestPrice: 4.22 },
        CUMBERLAND:   { expectedYield: 153.0, projectedPrice: 4.70, expectedRevenue: 719.10, harvestPrice: 4.22 },
        DAUPHIN:      { expectedYield: 158.6, projectedPrice: 4.70, expectedRevenue: 745.42, harvestPrice: 4.22 },
        DELAWARE:     { expectedYield: 177.4, projectedPrice: 4.70, expectedRevenue: 833.78, harvestPrice: 4.22 },
        ELK:          { expectedYield: 131.0, projectedPrice: 4.70, expectedRevenue: 615.70, harvestPrice: 4.22 },
        ERIE:         { expectedYield: 149.8, projectedPrice: 4.70, expectedRevenue: 704.06, harvestPrice: 4.22 },
        FAYETTE:      { expectedYield: 138.1, projectedPrice: 4.70, expectedRevenue: 649.07, harvestPrice: 4.22 },
        FOREST:       { expectedYield: 137.0, projectedPrice: 4.70, expectedRevenue: 643.90, harvestPrice: 4.22 },
        FRANKLIN:     { expectedYield: 143.8, projectedPrice: 4.70, expectedRevenue: 675.86, harvestPrice: 4.22 },
        FULTON:       { expectedYield: 125.0, projectedPrice: 4.70, expectedRevenue: 587.50, harvestPrice: 4.22 },
        GREENE:       { expectedYield: 132.0, projectedPrice: 4.70, expectedRevenue: 620.40, harvestPrice: 4.22 },
        HUNTINGDON:   { expectedYield: 144.9, projectedPrice: 4.70, expectedRevenue: 681.03, harvestPrice: 4.22 },
        INDIANA:      { expectedYield: 155.5, projectedPrice: 4.70, expectedRevenue: 730.85, harvestPrice: 4.22 },
        JEFFERSON:    { expectedYield: 133.8, projectedPrice: 4.70, expectedRevenue: 628.86, harvestPrice: 4.22 },
        JUNIATA:      { expectedYield: 128.4, projectedPrice: 4.70, expectedRevenue: 603.48, harvestPrice: 4.22 },
        LACKAWANNA:   { expectedYield: 133.2, projectedPrice: 4.70, expectedRevenue: 626.04, harvestPrice: 4.22 },
        LANCASTER:    { expectedYield: 203.4, projectedPrice: 4.70, expectedRevenue: 955.98, harvestPrice: 4.22 },
        LAWRENCE:     { expectedYield: 157.3, projectedPrice: 4.70, expectedRevenue: 739.31, harvestPrice: 4.22 },
        LEBANON:      { expectedYield: 187.3, projectedPrice: 4.70, expectedRevenue: 880.31, harvestPrice: 4.22 },
        LEHIGH:       { expectedYield: 161.7, projectedPrice: 4.70, expectedRevenue: 759.99, harvestPrice: 4.22 },
        LUZERNE:      { expectedYield: 149.4, projectedPrice: 4.70, expectedRevenue: 702.18, harvestPrice: 4.22 },
        LYCOMING:     { expectedYield: 146.5, projectedPrice: 4.70, expectedRevenue: 688.55, harvestPrice: 4.22 },
        MCKEAN:       { expectedYield: 152.4, projectedPrice: 4.70, expectedRevenue: 716.28, harvestPrice: 4.22 },
        MERCER:       { expectedYield: 157.2, projectedPrice: 4.70, expectedRevenue: 738.84, harvestPrice: 4.22 },
        MIFFLIN:      { expectedYield: 145.0, projectedPrice: 4.70, expectedRevenue: 681.50, harvestPrice: 4.22 },
        MONROE:       { expectedYield: 171.9, projectedPrice: 4.70, expectedRevenue: 807.93, harvestPrice: 4.22 },
        MONTGOMERY:   { expectedYield: 141.8, projectedPrice: 4.70, expectedRevenue: 666.46, harvestPrice: 4.22 },
        MONTOUR:      { expectedYield: 143.8, projectedPrice: 4.70, expectedRevenue: 675.86, harvestPrice: 4.22 },
        NORTHAMPTON:  { expectedYield: 169.0, projectedPrice: 4.70, expectedRevenue: 794.30, harvestPrice: 4.22 },
        NORTHUMBERLAND:{ expectedYield: 154.3, projectedPrice: 4.70, expectedRevenue: 725.21, harvestPrice: 4.22 },
        PERRY:        { expectedYield: 140.5, projectedPrice: 4.70, expectedRevenue: 660.35, harvestPrice: 4.22 },
        PIKE:         { expectedYield: 177.6, projectedPrice: 4.70, expectedRevenue: 834.72, harvestPrice: 4.22 },
        POTTER:       { expectedYield: 142.3, projectedPrice: 4.70, expectedRevenue: 668.81, harvestPrice: 4.22 },
        SCHUYLKILL:   { expectedYield: 143.0, projectedPrice: 4.70, expectedRevenue: 672.10, harvestPrice: 4.22 },
        SNYDER:       { expectedYield: 133.0, projectedPrice: 4.70, expectedRevenue: 625.10, harvestPrice: 4.22 },
        SOMERSET:     { expectedYield: 141.9, projectedPrice: 4.70, expectedRevenue: 666.93, harvestPrice: 4.22 },
        SULLIVAN:     { expectedYield: 137.0, projectedPrice: 4.70, expectedRevenue: 643.90, harvestPrice: 4.22 },
        SUSQUEHANNA:  { expectedYield: 129.1, projectedPrice: 4.70, expectedRevenue: 606.77, harvestPrice: 4.22 },
        TIOGA:        { expectedYield: 140.9, projectedPrice: 4.70, expectedRevenue: 662.23, harvestPrice: 4.22 },
        UNION:        { expectedYield: 154.7, projectedPrice: 4.70, expectedRevenue: 727.09, harvestPrice: 4.22 },
        VENANGO:      { expectedYield: 140.3, projectedPrice: 4.70, expectedRevenue: 659.41, harvestPrice: 4.22 },
        WARREN:       { expectedYield: 150.8, projectedPrice: 4.70, expectedRevenue: 708.76, harvestPrice: 4.22 },
        WASHINGTON:   { expectedYield: 141.8, projectedPrice: 4.70, expectedRevenue: 666.46, harvestPrice: 4.22 },
        WAYNE:        { expectedYield: 140.0, projectedPrice: 4.70, expectedRevenue: 658.00, harvestPrice: 4.22 },
        WESTMORELAND: { expectedYield: 141.6, projectedPrice: 4.70, expectedRevenue: 665.52, harvestPrice: 4.22 },
        WYOMING:      { expectedYield: 129.1, projectedPrice: 4.70, expectedRevenue: 606.77, harvestPrice: 4.22 },
        YORK:         { expectedYield: 177.7, projectedPrice: 4.70, expectedRevenue: 835.19, harvestPrice: 4.22 },
      },
    },
    SOYBEANS: {
      NY: {
        ALBANY:       { expectedYield: 50.2, finalCountyYield: 42.3, projectedPrice: 10.54, expectedRevenue: 529.11, harvestPrice: 10.35 },
        ALLEGANY:     { expectedYield: 55.7, finalCountyYield: 48.2, projectedPrice: 10.54, expectedRevenue: 587.08, harvestPrice: 10.35 },
        BROOME:       { expectedYield: 47.9, finalCountyYield: 40.6, projectedPrice: 10.54, expectedRevenue: 504.87, harvestPrice: 10.35 },
        CATTARAUGUS:  { expectedYield: 41.0, finalCountyYield: 32.3, projectedPrice: 10.54, expectedRevenue: 432.14, harvestPrice: 10.35 },
        CAYUGA:       { expectedYield: 50.8, finalCountyYield: 41.6, projectedPrice: 10.54, expectedRevenue: 535.43, harvestPrice: 10.35 },
        CHAUTAUQUA:   { expectedYield: 44.1, finalCountyYield: 36.1, projectedPrice: 10.54, expectedRevenue: 464.81, harvestPrice: 10.35 },
        CHEMUNG:      { expectedYield: 39.1, finalCountyYield: 34.7, projectedPrice: 10.54, expectedRevenue: 412.11, harvestPrice: 10.35 },
        CHENANGO:     { expectedYield: 46.6, finalCountyYield: 38.7, projectedPrice: 10.54, expectedRevenue: 491.16, harvestPrice: 10.35 },
        CLINTON:      { expectedYield: 40.1, finalCountyYield: 31.1, projectedPrice: 10.54, expectedRevenue: 422.65, harvestPrice: 10.35 },
        COLUMBIA:     { expectedYield: 47.4, finalCountyYield: 42.9, projectedPrice: 10.54, expectedRevenue: 499.60, harvestPrice: 10.35 },
        CORTLAND:     { expectedYield: 48.9, finalCountyYield: 40.6, projectedPrice: 10.54, expectedRevenue: 515.41, harvestPrice: 10.35 },
        DELAWARE:     { expectedYield: 47.2, finalCountyYield: 41.2, projectedPrice: 10.54, expectedRevenue: 497.49, harvestPrice: 10.35 },
        DUTCHESS:     { expectedYield: 54.2, finalCountyYield: 52, projectedPrice: 10.54, expectedRevenue: 571.27, harvestPrice: 10.35 },
        ERIE:         { expectedYield: 47.5, finalCountyYield: 33.4, projectedPrice: 10.54, expectedRevenue: 500.65, harvestPrice: 10.35 },
        ESSEX:        { expectedYield: 41.1, finalCountyYield: 35.1, projectedPrice: 10.54, expectedRevenue: 433.19, harvestPrice: 10.35 },
        FRANKLIN:     { expectedYield: 40.4, finalCountyYield: 32.5, projectedPrice: 10.54, expectedRevenue: 425.82, harvestPrice: 10.35 },
        FULTON:       { expectedYield: 43.9, finalCountyYield: 36.6, projectedPrice: 10.54, expectedRevenue: 462.71, harvestPrice: 10.35 },
        GENESEE:      { expectedYield: 51.8, finalCountyYield: 34.6, projectedPrice: 10.54, expectedRevenue: 545.97, harvestPrice: 10.35 },
        GREENE:       { expectedYield: 51.1, finalCountyYield: 44.5, projectedPrice: 10.54, expectedRevenue: 538.59, harvestPrice: 10.35 },
        HERKIMER:     { expectedYield: 44.2, finalCountyYield: 36.7, projectedPrice: 10.54, expectedRevenue: 465.87, harvestPrice: 10.35 },
        JEFFERSON:    { expectedYield: 38.6, finalCountyYield: 32, projectedPrice: 10.54, expectedRevenue: 406.84, harvestPrice: 10.35 },
        LEWIS:        { expectedYield: 42.6, finalCountyYield: 35.7, projectedPrice: 10.54, expectedRevenue: 449.00, harvestPrice: 10.35 },
        LIVINGSTON:   { expectedYield: 55.6, finalCountyYield: 50.2, projectedPrice: 10.54, expectedRevenue: 586.02, harvestPrice: 10.35 },
        MADISON:      { expectedYield: 46.4, finalCountyYield: 37.6, projectedPrice: 10.54, expectedRevenue: 489.06, harvestPrice: 10.35 },
        MONROE:       { expectedYield: 55.3, finalCountyYield: 43, projectedPrice: 10.54, expectedRevenue: 582.86, harvestPrice: 10.35 },
        MONTGOMERY:   { expectedYield: 43.9, finalCountyYield: 36.5, projectedPrice: 10.54, expectedRevenue: 462.71, harvestPrice: 10.35 },
        NIAGARA:      { expectedYield: 48.4, finalCountyYield: 41.4, projectedPrice: 10.54, expectedRevenue: 510.14, harvestPrice: 10.35 },
        ONEIDA:       { expectedYield: 45.1, finalCountyYield: 42.5, projectedPrice: 10.54, expectedRevenue: 475.35, harvestPrice: 10.35 },
        ONONDAGA:     { expectedYield: 49.2, finalCountyYield: 39.4, projectedPrice: 10.54, expectedRevenue: 518.57, harvestPrice: 10.35 },
        ONTARIO:      { expectedYield: 51.7, finalCountyYield: 43.2, projectedPrice: 10.54, expectedRevenue: 544.92, harvestPrice: 10.35 },
        ORANGE:       { expectedYield: 56.7, finalCountyYield: 53.2, projectedPrice: 10.54, expectedRevenue: 597.62, harvestPrice: 10.35 },
        ORLEANS:      { expectedYield: 53.4, finalCountyYield: 43.2, projectedPrice: 10.54, expectedRevenue: 562.84, harvestPrice: 10.35 },
        OSWEGO:       { expectedYield: 48.6, finalCountyYield: 42.3, projectedPrice: 10.54, expectedRevenue: 512.24, harvestPrice: 10.35 },
        OTSEGO:       { expectedYield: 45.7, finalCountyYield: 41.3, projectedPrice: 10.54, expectedRevenue: 481.68, harvestPrice: 10.35 },
        RENSSELAER:   { expectedYield: 44.4, finalCountyYield: 39.8, projectedPrice: 10.54, expectedRevenue: 467.98, harvestPrice: 10.35 },
        'ST LAWRENCE':{ expectedYield: 38.5, finalCountyYield: 33, projectedPrice: 10.54, expectedRevenue: 405.79, harvestPrice: 10.35 },
        SARATOGA:     { expectedYield: 44.0, finalCountyYield: 37.4, projectedPrice: 10.54, expectedRevenue: 463.76, harvestPrice: 10.35 },
        SCHENECTADY:  { expectedYield: 49.6, finalCountyYield: 44.2, projectedPrice: 10.54, expectedRevenue: 522.78, harvestPrice: 10.35 },
        SCHOHARIE:    { expectedYield: 46.8, finalCountyYield: 41.9, projectedPrice: 10.54, expectedRevenue: 493.27, harvestPrice: 10.35 },
        SCHUYLER:     { expectedYield: 52.8, finalCountyYield: 42.3, projectedPrice: 10.54, expectedRevenue: 556.51, harvestPrice: 10.35 },
        SENECA:       { expectedYield: 52.8, finalCountyYield: 42.5, projectedPrice: 10.54, expectedRevenue: 556.51, harvestPrice: 10.35 },
        STEUBEN:      { expectedYield: 49.9, finalCountyYield: 39.8, projectedPrice: 10.54, expectedRevenue: 525.95, harvestPrice: 10.35 },
        TIOGA:        { expectedYield: 46.7, finalCountyYield: 40.4, projectedPrice: 10.54, expectedRevenue: 492.22, harvestPrice: 10.35 },
        TOMPKINS:     { expectedYield: 52.4, finalCountyYield: 43.4, projectedPrice: 10.54, expectedRevenue: 552.30, harvestPrice: 10.35 },
        ULSTER:       { expectedYield: 49.3, finalCountyYield: 46.5, projectedPrice: 10.54, expectedRevenue: 519.62, harvestPrice: 10.35 },
        WASHINGTON:   { expectedYield: 46.8, finalCountyYield: 41.4, projectedPrice: 10.54, expectedRevenue: 493.27, harvestPrice: 10.35 },
        WAYNE:        { expectedYield: 51.5, finalCountyYield: 43.1, projectedPrice: 10.54, expectedRevenue: 542.81, harvestPrice: 10.35 },
        WYOMING:      { expectedYield: 51.6, finalCountyYield: 35.5, projectedPrice: 10.54, expectedRevenue: 543.86, harvestPrice: 10.35 },
        YATES:        { expectedYield: 51.9, finalCountyYield: 43, projectedPrice: 10.54, expectedRevenue: 547.03, harvestPrice: 10.35 },
      },
      PA: {
        ADAMS:        { expectedYield: 44.9, projectedPrice: 10.54, expectedRevenue: 473.25, harvestPrice: 10.35 },
        ALLEGHENY:    { expectedYield: 37.9, projectedPrice: 10.54, expectedRevenue: 399.47, harvestPrice: 10.35 },
        ARMSTRONG:    { expectedYield: 39.2, projectedPrice: 10.54, expectedRevenue: 413.17, harvestPrice: 10.35 },
        BEAVER:       { expectedYield: 40.5, projectedPrice: 10.54, expectedRevenue: 426.87, harvestPrice: 10.35 },
        BEDFORD:      { expectedYield: 48.3, projectedPrice: 10.54, expectedRevenue: 509.08, harvestPrice: 10.35 },
        BERKS:        { expectedYield: 54.3, projectedPrice: 10.54, expectedRevenue: 572.32, harvestPrice: 10.35 },
        BLAIR:        { expectedYield: 51.7, projectedPrice: 10.54, expectedRevenue: 544.92, harvestPrice: 10.35 },
        BRADFORD:     { expectedYield: 40.2, projectedPrice: 10.54, expectedRevenue: 423.71, harvestPrice: 10.35 },
        BUCKS:        { expectedYield: 46.8, projectedPrice: 10.54, expectedRevenue: 493.27, harvestPrice: 10.35 },
        BUTLER:       { expectedYield: 40.5, projectedPrice: 10.54, expectedRevenue: 426.87, harvestPrice: 10.35 },
        CAMBRIA:      { expectedYield: 49.6, projectedPrice: 10.54, expectedRevenue: 522.78, harvestPrice: 10.35 },
        CAMERON:      { expectedYield: 44.0, projectedPrice: 10.54, expectedRevenue: 463.76, harvestPrice: 10.35 },
        CARBON:       { expectedYield: 46.4, projectedPrice: 10.54, expectedRevenue: 489.06, harvestPrice: 10.35 },
        CENTRE:       { expectedYield: 49.8, projectedPrice: 10.54, expectedRevenue: 524.89, harvestPrice: 10.35 },
        CHESTER:      { expectedYield: 57.6, projectedPrice: 10.54, expectedRevenue: 607.10, harvestPrice: 10.35 },
        CLARION:      { expectedYield: 39.0, projectedPrice: 10.54, expectedRevenue: 411.06, harvestPrice: 10.35 },
        CLEARFIELD:   { expectedYield: 43.9, projectedPrice: 10.54, expectedRevenue: 462.71, harvestPrice: 10.35 },
        CLINTON:      { expectedYield: 49.9, projectedPrice: 10.54, expectedRevenue: 525.95, harvestPrice: 10.35 },
        COLUMBIA:     { expectedYield: 48.6, projectedPrice: 10.54, expectedRevenue: 512.24, harvestPrice: 10.35 },
        CRAWFORD:     { expectedYield: 39.3, projectedPrice: 10.54, expectedRevenue: 414.22, harvestPrice: 10.35 },
        CUMBERLAND:   { expectedYield: 49.7, projectedPrice: 10.54, expectedRevenue: 523.84, harvestPrice: 10.35 },
        DAUPHIN:      { expectedYield: 53.3, projectedPrice: 10.54, expectedRevenue: 561.78, harvestPrice: 10.35 },
        DELAWARE:     { expectedYield: 57.6, projectedPrice: 10.54, expectedRevenue: 607.10, harvestPrice: 10.35 },
        ELK:          { expectedYield: 43.0, projectedPrice: 10.54, expectedRevenue: 453.22, harvestPrice: 10.35 },
        ERIE:         { expectedYield: 41.9, projectedPrice: 10.54, expectedRevenue: 441.63, harvestPrice: 10.35 },
        FAYETTE:      { expectedYield: 40.9, projectedPrice: 10.54, expectedRevenue: 431.09, harvestPrice: 10.35 },
        FOREST:       { expectedYield: 44.0, projectedPrice: 10.54, expectedRevenue: 463.76, harvestPrice: 10.35 },
        FRANKLIN:     { expectedYield: 48.4, projectedPrice: 10.54, expectedRevenue: 510.14, harvestPrice: 10.35 },
        FULTON:       { expectedYield: 46.5, projectedPrice: 10.54, expectedRevenue: 490.11, harvestPrice: 10.35 },
        GREENE:       { expectedYield: 37.9, projectedPrice: 10.54, expectedRevenue: 399.47, harvestPrice: 10.35 },
        HUNTINGDON:   { expectedYield: 50.5, projectedPrice: 10.54, expectedRevenue: 532.27, harvestPrice: 10.35 },
        INDIANA:      { expectedYield: 46.1, projectedPrice: 10.54, expectedRevenue: 485.89, harvestPrice: 10.35 },
        JEFFERSON:    { expectedYield: 44.0, projectedPrice: 10.54, expectedRevenue: 463.76, harvestPrice: 10.35 },
        JUNIATA:      { expectedYield: 43.3, projectedPrice: 10.54, expectedRevenue: 456.38, harvestPrice: 10.35 },
        LACKAWANNA:   { expectedYield: 55.5, projectedPrice: 10.54, expectedRevenue: 584.97, harvestPrice: 10.35 },
        LANCASTER:    { expectedYield: 62.7, projectedPrice: 10.54, expectedRevenue: 660.86, harvestPrice: 10.35 },
        LAWRENCE:     { expectedYield: 41.8, projectedPrice: 10.54, expectedRevenue: 440.57, harvestPrice: 10.35 },
        LEBANON:      { expectedYield: 57.4, projectedPrice: 10.54, expectedRevenue: 605.00, harvestPrice: 10.35 },
        LEHIGH:       { expectedYield: 55.5, projectedPrice: 10.54, expectedRevenue: 584.97, harvestPrice: 10.35 },
        LUZERNE:      { expectedYield: 47.1, projectedPrice: 10.54, expectedRevenue: 496.43, harvestPrice: 10.35 },
        LYCOMING:     { expectedYield: 48.1, projectedPrice: 10.54, expectedRevenue: 506.97, harvestPrice: 10.35 },
        MCKEAN:       { expectedYield: 41.0, projectedPrice: 10.54, expectedRevenue: 432.14, harvestPrice: 10.35 },
        MERCER:       { expectedYield: 47.1, projectedPrice: 10.54, expectedRevenue: 496.43, harvestPrice: 10.35 },
        MIFFLIN:      { expectedYield: 51.4, projectedPrice: 10.54, expectedRevenue: 541.76, harvestPrice: 10.35 },
        MONROE:       { expectedYield: 57.2, projectedPrice: 10.54, expectedRevenue: 602.89, harvestPrice: 10.35 },
        MONTGOMERY:   { expectedYield: 44.9, projectedPrice: 10.54, expectedRevenue: 473.25, harvestPrice: 10.35 },
        MONTOUR:      { expectedYield: 46.7, projectedPrice: 10.54, expectedRevenue: 492.22, harvestPrice: 10.35 },
        NORTHAMPTON:  { expectedYield: 52.8, projectedPrice: 10.54, expectedRevenue: 556.51, harvestPrice: 10.35 },
        NORTHUMBERLAND:{ expectedYield: 51.8, projectedPrice: 10.54, expectedRevenue: 545.97, harvestPrice: 10.35 },
        PERRY:        { expectedYield: 43.9, projectedPrice: 10.54, expectedRevenue: 462.71, harvestPrice: 10.35 },
        PIKE:         { expectedYield: 55.5, projectedPrice: 10.54, expectedRevenue: 584.97, harvestPrice: 10.35 },
        POTTER:       { expectedYield: 40.2, projectedPrice: 10.54, expectedRevenue: 423.71, harvestPrice: 10.35 },
        SCHUYLKILL:   { expectedYield: 47.4, projectedPrice: 10.54, expectedRevenue: 499.60, harvestPrice: 10.35 },
        SNYDER:       { expectedYield: 42.8, projectedPrice: 10.54, expectedRevenue: 451.11, harvestPrice: 10.35 },
        SOMERSET:     { expectedYield: 43.3, projectedPrice: 10.54, expectedRevenue: 456.38, harvestPrice: 10.35 },
        SULLIVAN:     { expectedYield: 47.9, projectedPrice: 10.54, expectedRevenue: 504.87, harvestPrice: 10.35 },
        SUSQUEHANNA:  { expectedYield: 47.9, projectedPrice: 10.54, expectedRevenue: 504.87, harvestPrice: 10.35 },
        TIOGA:        { expectedYield: 40.2, projectedPrice: 10.54, expectedRevenue: 423.71, harvestPrice: 10.35 },
        UNION:        { expectedYield: 50.7, projectedPrice: 10.54, expectedRevenue: 534.38, harvestPrice: 10.35 },
        VENANGO:      { expectedYield: 39.4, projectedPrice: 10.54, expectedRevenue: 415.28, harvestPrice: 10.35 },
        WARREN:       { expectedYield: 40.9, projectedPrice: 10.54, expectedRevenue: 431.09, harvestPrice: 10.35 },
        WASHINGTON:   { expectedYield: 38.6, projectedPrice: 10.54, expectedRevenue: 406.84, harvestPrice: 10.35 },
        WAYNE:        { expectedYield: 55.5, projectedPrice: 10.54, expectedRevenue: 584.97, harvestPrice: 10.35 },
        WESTMORELAND: { expectedYield: 42.2, projectedPrice: 10.54, expectedRevenue: 444.79, harvestPrice: 10.35 },
        WYOMING:      { expectedYield: 44.8, projectedPrice: 10.54, expectedRevenue: 472.19, harvestPrice: 10.35 },
        YORK:         { expectedYield: 52.7, projectedPrice: 10.54, expectedRevenue: 555.46, harvestPrice: 10.35 },
      },
    },
    WHEAT: {
      NY: {
        ALLEGANY:     { expectedYield: 74.1, finalCountyYield: 82.9, projectedPrice: 6.06, expectedRevenue: 449.05, harvestPrice: 5.43 },
        CATTARAUGUS:  { expectedYield: 71.0, finalCountyYield: 76.6, projectedPrice: 6.06, expectedRevenue: 430.26, harvestPrice: 5.43 },
        CAYUGA:       { expectedYield: 70.3, finalCountyYield: 72.2, projectedPrice: 6.06, expectedRevenue: 426.02, harvestPrice: 5.43 },
        CHAUTAUQUA:   { expectedYield: 65.4, finalCountyYield: 75.3, projectedPrice: 6.06, expectedRevenue: 396.32, harvestPrice: 5.43 },
        COLUMBIA:     { expectedYield: 73.7, finalCountyYield: 76.4, projectedPrice: 6.06, expectedRevenue: 446.62, harvestPrice: 5.43 },
        CORTLAND:     { expectedYield: 69.9, finalCountyYield: 66.4, projectedPrice: 6.06, expectedRevenue: 423.59, harvestPrice: 5.43 },
        ERIE:         { expectedYield: 67.7, finalCountyYield: 79.0, projectedPrice: 6.06, expectedRevenue: 410.26, harvestPrice: 5.43 },
        GENESEE:      { expectedYield: 75.3, finalCountyYield: 80.0, projectedPrice: 6.06, expectedRevenue: 456.32, harvestPrice: 5.43 },
        JEFFERSON:    { expectedYield: 69.6, finalCountyYield: 66.2, projectedPrice: 6.06, expectedRevenue: 421.78, harvestPrice: 5.43 },
        LIVINGSTON:   { expectedYield: 74.0, finalCountyYield: 85.8, projectedPrice: 6.06, expectedRevenue: 448.44, harvestPrice: 5.43 },
        MADISON:      { expectedYield: 69.0, finalCountyYield: 66.2, projectedPrice: 6.06, expectedRevenue: 418.14, harvestPrice: 5.43 },
        MONROE:       { expectedYield: 77.6, finalCountyYield: 81.1, projectedPrice: 6.06, expectedRevenue: 470.26, harvestPrice: 5.43 },
        MONTGOMERY:   { expectedYield: 68.8, finalCountyYield: 63.8, projectedPrice: 6.06, expectedRevenue: 416.93, harvestPrice: 5.43 },
        NIAGARA:      { expectedYield: 71.7, finalCountyYield: 80.4, projectedPrice: 6.06, expectedRevenue: 434.50, harvestPrice: 5.43 },
        ONEIDA:       { expectedYield: 69.4, finalCountyYield: 66.1, projectedPrice: 6.06, expectedRevenue: 420.56, harvestPrice: 5.43 },
        ONONDAGA:     { expectedYield: 69.9, finalCountyYield: 66.4, projectedPrice: 6.06, expectedRevenue: 423.59, harvestPrice: 5.43 },
        ONTARIO:      { expectedYield: 76.6, finalCountyYield: 82.6, projectedPrice: 6.06, expectedRevenue: 464.20, harvestPrice: 5.43 },
        ORLEANS:      { expectedYield: 74.6, finalCountyYield: 81.2, projectedPrice: 6.06, expectedRevenue: 452.08, harvestPrice: 5.43 },
        OSWEGO:       { expectedYield: 69.6, finalCountyYield: 66.2, projectedPrice: 6.06, expectedRevenue: 421.78, harvestPrice: 5.43 },
        SCHUYLER:     { expectedYield: 73.7, finalCountyYield: 72.0, projectedPrice: 6.06, expectedRevenue: 446.62, harvestPrice: 5.43 },
        SENECA:       { expectedYield: 73.4, finalCountyYield: 76.8, projectedPrice: 6.06, expectedRevenue: 444.80, harvestPrice: 5.43 },
        STEUBEN:      { expectedYield: 74.1, finalCountyYield: 81.4, projectedPrice: 6.06, expectedRevenue: 449.05, harvestPrice: 5.43 },
        SUFFOLK:      { expectedYield: 73.7, finalCountyYield: 76.4, projectedPrice: 6.06, expectedRevenue: 446.62, harvestPrice: 5.43 },
        TOMPKINS:     { expectedYield: 73.0, finalCountyYield: 70.5, projectedPrice: 6.06, expectedRevenue: 442.38, harvestPrice: 5.43 },
        WASHINGTON:   { expectedYield: 73.7, finalCountyYield: 76.4, projectedPrice: 6.06, expectedRevenue: 446.62, harvestPrice: 5.43 },
        WAYNE:        { expectedYield: 74.1, finalCountyYield: 81.7, projectedPrice: 6.06, expectedRevenue: 449.05, harvestPrice: 5.43 },
        WYOMING:      { expectedYield: 75.0, finalCountyYield: 84.4, projectedPrice: 6.06, expectedRevenue: 454.50, harvestPrice: 5.43 },
        YATES:        { expectedYield: 74.7, finalCountyYield: 76.8, projectedPrice: 6.06, expectedRevenue: 452.68, harvestPrice: 5.43 },
      },
      PA: {
        ADAMS:        { expectedYield: 69.0, finalCountyYield: 58.9, projectedPrice: 6.06, expectedRevenue: 418.14, harvestPrice: 5.43 },
        ALLEGHENY:    { expectedYield: 60.1, finalCountyYield: 54.5, projectedPrice: 6.06, expectedRevenue: 364.21, harvestPrice: 5.43 },
        ARMSTRONG:    { expectedYield: 55.9, finalCountyYield: 46.1, projectedPrice: 6.06, expectedRevenue: 338.75, harvestPrice: 5.43 },
        BEAVER:       { expectedYield: 61.9, finalCountyYield: 64.7, projectedPrice: 6.06, expectedRevenue: 375.11, harvestPrice: 5.43 },
        BEDFORD:      { expectedYield: 71.3, finalCountyYield: 57.1, projectedPrice: 6.06, expectedRevenue: 432.08, harvestPrice: 5.43 },
        BERKS:        { expectedYield: 85.0, finalCountyYield: 66.0, projectedPrice: 6.06, expectedRevenue: 515.10, harvestPrice: 5.43 },
        BLAIR:        { expectedYield: 70.8, finalCountyYield: 57.1, projectedPrice: 6.06, expectedRevenue: 429.05, harvestPrice: 5.43 },
        BRADFORD:     { expectedYield: 70.6, finalCountyYield: 56.4, projectedPrice: 6.06, expectedRevenue: 427.84, harvestPrice: 5.43 },
        BUCKS:        { expectedYield: 67.5, finalCountyYield: 54.8, projectedPrice: 6.06, expectedRevenue: 409.05, harvestPrice: 5.43 },
        BUTLER:       { expectedYield: 59.2, finalCountyYield: 55.4, projectedPrice: 6.06, expectedRevenue: 358.75, harvestPrice: 5.43 },
        CAMBRIA:      { expectedYield: 68.2, finalCountyYield: 51.7, projectedPrice: 6.06, expectedRevenue: 413.29, harvestPrice: 5.43 },
        CARBON:       { expectedYield: 74.0, finalCountyYield: 58.6, projectedPrice: 6.06, expectedRevenue: 448.44, harvestPrice: 5.43 },
        CENTRE:       { expectedYield: 73.1, finalCountyYield: 57.8, projectedPrice: 6.06, expectedRevenue: 442.99, harvestPrice: 5.43 },
        CHESTER:      { expectedYield: 88.7, finalCountyYield: 81.0, projectedPrice: 6.06, expectedRevenue: 537.52, harvestPrice: 5.43 },
        CLARION:      { expectedYield: 60.4, finalCountyYield: 54.0, projectedPrice: 6.06, expectedRevenue: 366.02, harvestPrice: 5.43 },
        CLEARFIELD:   { expectedYield: 69.2, finalCountyYield: 50.0, projectedPrice: 6.06, expectedRevenue: 419.35, harvestPrice: 5.43 },
        CLINTON:      { expectedYield: 69.9, finalCountyYield: 50.9, projectedPrice: 6.06, expectedRevenue: 423.59, harvestPrice: 5.43 },
        COLUMBIA:     { expectedYield: 74.3, finalCountyYield: 57.1, projectedPrice: 6.06, expectedRevenue: 450.26, harvestPrice: 5.43 },
        CRAWFORD:     { expectedYield: 57.8, finalCountyYield: 54.9, projectedPrice: 6.06, expectedRevenue: 350.27, harvestPrice: 5.43 },
        CUMBERLAND:   { expectedYield: 88.8, finalCountyYield: 86.7, projectedPrice: 6.06, expectedRevenue: 538.13, harvestPrice: 5.43 },
        DAUPHIN:      { expectedYield: 89.4, finalCountyYield: 86.6, projectedPrice: 6.06, expectedRevenue: 541.76, harvestPrice: 5.43 },
        ERIE:         { expectedYield: 55.1, finalCountyYield: 53.0, projectedPrice: 6.06, expectedRevenue: 333.91, harvestPrice: 5.43 },
        FAYETTE:      { expectedYield: 66.7, finalCountyYield: 52.6, projectedPrice: 6.06, expectedRevenue: 404.20, harvestPrice: 5.43 },
        FRANKLIN:     { expectedYield: 72.9, finalCountyYield: 66.8, projectedPrice: 6.06, expectedRevenue: 441.77, harvestPrice: 5.43 },
        FULTON:       { expectedYield: 74.9, finalCountyYield: 60.2, projectedPrice: 6.06, expectedRevenue: 453.89, harvestPrice: 5.43 },
        GREENE:       { expectedYield: 63.5, finalCountyYield: 57.1, projectedPrice: 6.06, expectedRevenue: 384.81, harvestPrice: 5.43 },
        HUNTINGDON:   { expectedYield: 75.9, finalCountyYield: 64.9, projectedPrice: 6.06, expectedRevenue: 459.95, harvestPrice: 5.43 },
        INDIANA:      { expectedYield: 64.1, finalCountyYield: 50.6, projectedPrice: 6.06, expectedRevenue: 388.45, harvestPrice: 5.43 },
        JEFFERSON:    { expectedYield: 64.3, finalCountyYield: 49.8, projectedPrice: 6.06, expectedRevenue: 389.66, harvestPrice: 5.43 },
        JUNIATA:      { expectedYield: 77.4, finalCountyYield: 69.0, projectedPrice: 6.06, expectedRevenue: 469.04, harvestPrice: 5.43 },
        LACKAWANNA:   { expectedYield: 67.2, finalCountyYield: 54.6, projectedPrice: 6.06, expectedRevenue: 407.23, harvestPrice: 5.43 },
        LANCASTER:    { expectedYield: 93.8, finalCountyYield: 86.2, projectedPrice: 6.06, expectedRevenue: 568.43, harvestPrice: 5.43 },
        LAWRENCE:     { expectedYield: 59.8, finalCountyYield: 60.7, projectedPrice: 6.06, expectedRevenue: 362.39, harvestPrice: 5.43 },
        LEBANON:      { expectedYield: 85.5, finalCountyYield: 69.0, projectedPrice: 6.06, expectedRevenue: 518.13, harvestPrice: 5.43 },
        LEHIGH:       { expectedYield: 68.0, finalCountyYield: 52.7, projectedPrice: 6.06, expectedRevenue: 412.08, harvestPrice: 5.43 },
        LUZERNE:      { expectedYield: 73.0, finalCountyYield: 58.6, projectedPrice: 6.06, expectedRevenue: 442.38, harvestPrice: 5.43 },
        LYCOMING:     { expectedYield: 72.4, finalCountyYield: 56.0, projectedPrice: 6.06, expectedRevenue: 438.74, harvestPrice: 5.43 },
        MERCER:       { expectedYield: 59.4, finalCountyYield: 60.9, projectedPrice: 6.06, expectedRevenue: 359.96, harvestPrice: 5.43 },
        MIFFLIN:      { expectedYield: 74.8, finalCountyYield: 62.1, projectedPrice: 6.06, expectedRevenue: 453.29, harvestPrice: 5.43 },
        MONROE:       { expectedYield: 68.3, finalCountyYield: 54.3, projectedPrice: 6.06, expectedRevenue: 413.90, harvestPrice: 5.43 },
        MONTGOMERY:   { expectedYield: 81.0, finalCountyYield: 72.2, projectedPrice: 6.06, expectedRevenue: 490.86, harvestPrice: 5.43 },
        MONTOUR:      { expectedYield: 74.2, finalCountyYield: 59.1, projectedPrice: 6.06, expectedRevenue: 449.65, harvestPrice: 5.43 },
        NORTHAMPTON:  { expectedYield: 69.0, finalCountyYield: 56.8, projectedPrice: 6.06, expectedRevenue: 418.14, harvestPrice: 5.43 },
        NORTHUMBERLAND:{ expectedYield: 75.0, finalCountyYield: 59.6, projectedPrice: 6.06, expectedRevenue: 454.50, harvestPrice: 5.43 },
        PERRY:        { expectedYield: 78.4, finalCountyYield: 69.4, projectedPrice: 6.06, expectedRevenue: 475.10, harvestPrice: 5.43 },
        POTTER:       { expectedYield: 72.6, finalCountyYield: 77.7, projectedPrice: 6.06, expectedRevenue: 439.96, harvestPrice: 5.43 },
        SCHUYLKILL:   { expectedYield: 76.2, finalCountyYield: 58.7, projectedPrice: 6.06, expectedRevenue: 461.77, harvestPrice: 5.43 },
        SNYDER:       { expectedYield: 78.0, finalCountyYield: 65.8, projectedPrice: 6.06, expectedRevenue: 472.68, harvestPrice: 5.43 },
        SOMERSET:     { expectedYield: 69.1, finalCountyYield: 51.4, projectedPrice: 6.06, expectedRevenue: 418.75, harvestPrice: 5.43 },
        TIOGA:        { expectedYield: 73.6, finalCountyYield: 78.1, projectedPrice: 6.06, expectedRevenue: 446.02, harvestPrice: 5.43 },
        UNION:        { expectedYield: 73.3, finalCountyYield: 55.3, projectedPrice: 6.06, expectedRevenue: 444.20, harvestPrice: 5.43 },
        VENANGO:      { expectedYield: 58.1, finalCountyYield: 53.9, projectedPrice: 6.06, expectedRevenue: 352.09, harvestPrice: 5.43 },
        WASHINGTON:   { expectedYield: 63.2, finalCountyYield: 59.0, projectedPrice: 6.06, expectedRevenue: 382.99, harvestPrice: 5.43 },
        WAYNE:        { expectedYield: 67.2, finalCountyYield: 53.5, projectedPrice: 6.06, expectedRevenue: 407.23, harvestPrice: 5.43 },
        WESTMORELAND: { expectedYield: 61.8, finalCountyYield: 50.0, projectedPrice: 6.06, expectedRevenue: 374.51, harvestPrice: 5.43 },
        WYOMING:      { expectedYield: 73.0, finalCountyYield: 58.9, projectedPrice: 6.06, expectedRevenue: 442.38, harvestPrice: 5.43 },
        YORK:         { expectedYield: 91.4, finalCountyYield: 89.0, projectedPrice: 6.06, expectedRevenue: 553.88, harvestPrice: 5.43 },
      },
    },
  },
};

// SCO/ECO trigger bands. RMA's actual product structure:
//   - SCO (Plan 51 YP / 52 RP / 53 RP-HPE): covers down from its top to the
//     underlying MPCI coverage level. The SCO bottom is the farmer's MPCI %,
//     passed in at compute time as `scoUnderlying` — 75% is the most common in
//     this book and the default.
//   - ECO 95% (top=95, bottom = the SCO top) — what 100% of our ECO book carries
//   - ECO 90% (top=90, bottom = the SCO top) — shown for reference; rare in book
// ECO 95% and ECO 90% are mutually exclusive — a farmer picks one. Both stack
// on top of SCO. Coverage range = top − bottom in coverage-pct points.
//
// ⚠ THE TOP IS A CROP-YEAR ANSWER, NOT A CONSTANT — 86 through CY2026, 90 from
// CY2027 (RMA PM-26-036). It comes from `scoEcoBands`, the one rule every band
// surface in this file reads, so a tracker scoring a 2027 settlement can never
// score it on the retired 86% structure. See src/scoEcoBands.js.
const scoEcoTriggerBands = (cropYear) => {
  const b = scoEcoBands(cropYear);
  const top = Math.round(b.scoTop * 100);
  return [
    { key: 'sco',   label: 'SCO',     top },                    // bottom = scoUnderlying (dynamic)
    { key: 'eco95', label: 'ECO 95%', top: 95, bottom: top },
    { key: 'eco90', label: 'ECO 90%', top: 90, bottom: top },
  ];
};

// Given a county's actual revenue ($/ac) and expected revenue ($/ac), return
// the % of max liability that pays out under each SCO/ECO band.
//
// Formula:
//   ratio = actual / expected
//   lossPts = max(0, topTrigger − ratio×100)
//   payoutPct = min(lossPts, range) / range × 100
//
// `scoUnderlying` is the SCO floor (= farmer's MPCI coverage %). Most of this
// book is 75%, so the default lives here; the UI selector overrides.
//
// `cropYear` decides where SCO stops and ECO starts and is REQUIRED — this
// tracker is pointed at a settled year, and scoring a 2027 county on the 86%
// structure understates every SCO payout by four points of revenue.
//
// All bands cap at 100% (lossPts can exceed range when revenue craters; the
// indemnity is capped at the band's liability). When actualRevenue is null
// (no book yield in this county), returns nulls so the UI can render "—".
function computeScoEcoTrigger({ actualRevenue, expectedRevenue, scoUnderlying = 75, cropYear }) {
  const BANDS = scoEcoTriggerBands(cropYear);
  if (actualRevenue == null || !expectedRevenue) {
    return { ratio: null, payouts: BANDS.reduce((m, b) => ({ ...m, [b.key]: null }), {}) };
  }
  const ratio = actualRevenue / expectedRevenue;
  const ratioPct = ratio * 100;
  const payouts = {};
  for (const band of BANDS) {
    const bottom = band.key === 'sco' ? scoUnderlying : band.bottom;
    const range = band.top - bottom;
    const lossPts = Math.max(0, band.top - ratioPct);
    payouts[band.key] = range > 0 ? Math.min(lossPts, range) / range * 100 : null;
  }
  return { ratio, payouts };
}

// ── Historical SCO/ECO backtest — TODAY'S product structure (OBBBA / 2026+) ────
// The bands here are the CURRENT product, applied to every historical year, to
// answer "if I'd owned today's ECO/SCO every year, what would it have paid?"
//   - SCO covers the farm's MPCI coverage level → 90% (was 86% pre-2027 —
//     RMA PM-26-036 makes the change effective for the 2027 crop year, NOT 2026;
//     an earlier version of this comment said 2026 and was wrong, which is the
//     same off-by-one that drew every 2026 bar with 2027 geometry).
//   - ECO covers 90% → 95% (the 5-pt band above SCO's new 90% top).
// Both are AREA-revenue triggers: the payment FACTOR (0–1) is purely county/
// year-driven off the county revenue ratio; the $ liability scales with the
// insured's OWN expected crop value (aph × that year's projected price) and the
// elected coverage % (50–100). This deliberately IGNORES the stored
// sco_payout_pct/eco90/eco95 columns — those were computed on the retired 86%
// bottom (verified: Monroe corn 2025 ratio 77.8 → stored SCO 74 = the 75→86
// band). We recompute from revenue_ratio, which is structure-independent.
//
// ratioPct is a percent (e.g. 77.8 = final county rev is 77.8% of expected).
// Returns per-acre coverage + indemnity for SCO and ECO, or null if no ratio.
function backtestScoEco({ ratioPct, baseClPct = 75, aph = null, projPrice = null, scoCovPct = 100, ecoCovPct = 100 }) {
  if (ratioPct == null || !isFinite(+ratioPct)) return null;
  const r = +ratioPct;
  // Deliberately the CURRENT structure for every historical year — that is what
  // "if I'd owned today's product" means. Read from the one rule rather than
  // typed here, so the day RMA moves the band again this moves with it.
  const _b = scoEcoBands(SCO_ECO_BAND_CHANGE_YEAR);
  const SCO_TOP = Math.round(_b.scoTop * 100), ECO_TOP = Math.round(_b.ecoTop * 100), ECO_BOTTOM = Math.round(_b.ecoBottom * 100);
  const scoBandPts = SCO_TOP - baseClPct;                 // e.g. 90 − 75 = 15 pts
  const ecoBandPts = ECO_TOP - ECO_BOTTOM;               // 5 pts
  const scoFactor = scoBandPts > 0 ? Math.min(Math.max(0, SCO_TOP - r), scoBandPts) / scoBandPts : 0;
  const ecoFactor = Math.min(Math.max(0, ECO_TOP - r), ecoBandPts) / ecoBandPts;
  const ecvAc = (aph != null && projPrice != null && isFinite(+aph) && isFinite(+projPrice)) ? +aph * +projPrice : null;
  const scoCovAc = ecvAc != null ? (scoBandPts / 100) * ecvAc * (scoCovPct / 100) : null;   // max SCO liability $/ac
  const ecoCovAc = ecvAc != null ? (ecoBandPts / 100) * ecvAc * (ecoCovPct / 100) : null;   // max ECO liability $/ac
  return {
    scoFactor, ecoFactor, scoCovAc, ecoCovAc,
    scoIndemAc: scoCovAc != null ? scoFactor * scoCovAc : null,
    ecoIndemAc: ecoCovAc != null ? ecoFactor * ecoCovAc : null,
  };
}

// Names a SCO/ECO band selection. One helper so the backtest card header, the
// page intro above it and the card's own footnote can never disagree about which
// bands the numbers on screen actually cover. Single object argument on purpose —
// nothing here can be dropped at a call site.
// The backtest it labels applies TODAY's product to every year, so it names
// today's bands — but it takes them from the shared rule rather than spelling
// them out, so the label cannot drift from the arithmetic beside it.
function btBandLabel({ sco, eco, baseClPct = null }) {
  return scoEcoBandLabel({ cropYear: SCO_ECO_BAND_CHANGE_YEAR, sco, eco, baseClPct });
}

// Dry bean prices (global, $/lb, manually updated from RMA bulletins)
// Types WITH revenue protection: BTS, DRK, PNAV, SMR (harvest price applies)
// Types WITHOUT revenue protection: LRK, CBRY, WK, GRNOTH (price set once/year)
const DRY_BEAN_PRICE_KEY = "dry_bean_prices_global";
const DEFAULT_DRY_BEAN_PRICES = {
  // Projected prices in $/cwt (i.e. $/lb × 100) — matches the gpa calc which does yield(lbs) × price ÷ 100 × coverage
  // Source: RMA 2026 price release ($/lb values × 100)
  BTS:    29.00,  // Black turtle soup (Black - Conventional) $0.29/lb
  DRK:    44.00,  // Dark red kidney - Conventional $0.44/lb
  LRK:    43.00,  // Light red kidney - Conventional $0.43/lb
  CBRY:   39.00,  // Cranberry - Conventional $0.39/lb
  PNAV:   31.00,  // Pea (Navy) - Conventional $0.31/lb
  SMR:    28.00,  // Pinto - Conventional $0.28/lb (closest match)
  WK:     46.00,  // White kidney - Conventional $0.46/lb
  GRNOTH: 29.00,  // Other — default to Black Conventional
  // Organic variants
  BTS_ORGANIC:  58.00,  // Black - Organic $0.58/lb
  DRK_ORGANIC:  88.00,  // Dark Red Kidney - Organic $0.88/lb
  LRK_ORGANIC:  86.00,  // Light Red Kidney - Organic $0.86/lb
  CBRY_ORGANIC: 78.00,  // Cranberry - Organic $0.78/lb
  PNAV_ORGANIC: 62.00,  // Pea (Navy) - Organic $0.62/lb
  SMR_ORGANIC:  56.00,  // Pinto - Organic $0.56/lb
  WK_ORGANIC:   92.00,  // White Kidney - Organic $0.92/lb
};

// Corn silage (GSS) has its own RMA projected price per TON (not per bushel like grain)
// Source: RMA 2026 price release
const RMA_SILAGE_PRICES = {
  2026: 38.25,
  2025: 36.00,  // approximate — update if needed
};

// DEFAULT_PRICES_BY_YEAR — used by the prices state for revenue + PP calcs.
// Dry beans handled separately via dryBeanPrices state. Cabbage variants
// (Fresh/Proc × Conv/Organic) live in CABBAGE_PRICES — `prices['CABBAGE']`
// is the default (Fresh Conventional) used when a row lacks variant info.
// VERIFY annotations mark placeholders that should be confirmed against the
// year's RMA Special Provisions or county-specific actuarial documents.
const DEFAULT_PRICES_BY_YEAR = {
  2026: {
    CORN: 4.62, SOYBEANS: 11.09, WHEAT: 5.76, BARLEY: 5.45,
    // ⚠ TRITICALE 5.76 -> 3.51 and OATS 4.08 -> 3.30, 2026-09-12. THIS TABLE IS A
    // THIRD COPY OF THE PRICES AND IT HAD MISSED BOTH CORRECTIONS.
    //
    // RMA_SPRING_PRICES was corrected on 2026-08-11 (oats) and 2026-08-12
    // (triticale, which "had been carrying WHEAT's number"), and this table — the
    // one its own comment says SEEDS THE `prices` STATE — kept the old values. The
    // final branch of getCropPriceAndUnit reads `prices[r.crop]` BEFORE
    // effectivePrice(), so the stale constant shadowed both the corrected constant
    // and the ADM feed, which carries triticale at 3.51 on all 318 CY2026 rows
    // across NY/PA/OH/MD.
    //
    // It reached a grower-facing figure. AUSTIC FARM PARTNERS' Fall card printed a
    // triticale bushel guarantee of 6,899 bu — that is exactly
    // liability 21,855 / (5.76 x 0.55 CAT), where the right answer is
    // 21,855 / (3.51 x 0.55) = 11,321, and 11,321 is ALSO what
    // aph 99 x coverage 0.50 x 228.7 ac gives. The two methods agree the moment the
    // price is right, and they are 39% apart while it is wrong. Wheat on the same
    // card is the control: its two methods already agree to the bushel (73,125 vs
    // 73,121 on the Seneca row), because 5.76 is genuinely wheat's price.
    //
    // The bushel guarantee is the pre-harvest forward-sell ceiling, so understating
    // it is the direction that quietly costs a grower bushels they could have sold.
    // Measured: 10 coverages across 7 farms — TRITICALE 2 farms, 5,131 bu (39% low);
    // OATS 5 farms, 5,826 bu (19% low).
    TRITICALE: 3.51, OATS: 3.30, "GRAIN SORGHUM": 4.61,
    POTATOES: 17.85, POTATOES_ORGANIC: 33.60,
    CABBAGE: 24.55,                       // Fresh Conventional default
    CABBAGE_FRESH_CONVENTIONAL: 24.55,
    CABBAGE_FRESH_ORGANIC:      52.54,
    CABBAGE_PROC_CONVENTIONAL:   4.30,
    CABBAGE_PROC_ORGANIC:        9.20,
    "SWEET CORN": 130.00,     // VERIFY: NY sweet corn processing — $/ton
    "PROCESSING BEANS": 365.00, // VERIFY: NY snap beans contract — $/ton
    CORN_ORGANIC: 7.30, SOYBEANS_ORGANIC: 21.57, WHEAT_ORGANIC: 11.16,
  },
  // ⚠ EVERY HISTORICAL WHEAT PRICE HERE NOW CARRIES ITS SOURCE. They are NOT
  // decoration: this table seeds the `prices` state and is preferred over the price
  // ladder (see getCropPriceAndUnit), so a wrong value is never cross-checked
  // against the ADM feed — which is how triticale came to be priced as wheat and
  // reach a grower's bushel guarantee 39% low. A constant that decides customer
  // money gets the same provenance discipline as an APH, an acre or a liability.
  //
  // CY2025 WHEAT 5.56 -> 6.06, CY2023 WHEAT 8.70 -> 8.45, both 2026-09-12, sourced
  // by Kevin against RMA's own Price Discovery bulletins. Each now agrees with
  // RMA_SPRING_PRICES, which had carried the correct figure all along.
  //
  // CY2024 WHEAT 6.59 -> 6.72, settled against the record rather than by making two
  // tables agree. RMA PM-23-054's own attachment, under "Projected Prices for the
  // August 15, 2023 - September 14, 2023, Discovery Period", carries the row
  //
  //     Wheat   New York   9/30/2023   Winter   6.72
  //
  // — the September 30 sales closing this state's winter wheat is written at. Ohio
  // and Pennsylvania read 6.72 on the same page, which is the cross-check that the
  // figure is the regional one and not a mis-read line.
  2025: { CORN: 4.70, SOYBEANS: 10.54, WHEAT: 6.06, POTATOES: 10.00, CABBAGE: 24.55 },   // WHEAT: RMA PM-24-060 (NY winter wheat, projected)
  2024: { CORN: 4.66, SOYBEANS: 11.55, WHEAT: 6.72, POTATOES: 10.00 },                   // WHEAT: RMA PM-23-054 attachment, NY winter, 9/30/2023 SCD
  2023: { CORN: 5.91, SOYBEANS: 13.76, WHEAT: 8.45, POTATOES: 10.00 },                   // WHEAT: RMA PM-22-056 (NY winter wheat, projected)
};
const CROP_COLOR = { CORN: "#c8860a", SOYBEANS: "#3a6e32", "DRY BEANS": "#7a3b10", WHEAT: "#6b4f00", BARLEY: "#7a6530", "PROCESSING BEANS": "#5a3a10", "GRAIN SORGHUM": "#a06010", POTATOES: "#7a5a30", OATS: "#9a8030", TRITICALE: "#7a6020", CABBAGE: "#3a7a4a", "SWEET CORN": "#d9a020" };
const CROP_BG    = { CORN: "#fef9ec", SOYBEANS: "#eef6ec", "DRY BEANS": "#fdf1e8", WHEAT: "#f8f4ec", BARLEY: "#f6f2e8", "PROCESSING BEANS": "#f8ece4", "GRAIN SORGHUM": "#fdf5e8", POTATOES: "#f8f2e8", OATS: "#fbf6e8", TRITICALE: "#f8f4ec", CABBAGE: "#ecf6ec", "SWEET CORN": "#fef6e8" };

// Normalize practice strings from any source to canonical values:
// "Organic" for any organic/certified organic variant, "Non-Irrigated" for conventional
function normalizePractice(p) {
  if (!p) return "Non-Irrigated";
  const u = p.toUpperCase().trim();
  if (u === "") return "Non-Irrigated";
  if (u.includes("ORGANIC") || u === "NON-IRROC" || u === "NONIRROC") return "Organic";
  if (u.includes("NON") && u.includes("IRR")) return "Non-Irrigated";
  if (u === "A" || u === "NON IRR") return "Non-Irrigated";
  if (u === "NIRR") return "Non-Irrigated";
  if (u === "IRR") return "Irrigated";
  return p; // pass through unknown values
}

// Map type variants to a canonical RMA numeric code for matching across
// crop_coverage (short codes: GSG/GSS/NTS/W) vs pp_eligibility (named
// or named-with-suffix: Grain/Grain-016/Silage-026/No Type Specified-997).
// Returns "" for blank/unknown — preserves display string, only used for matching.
const _TYPE_NAME_TO_CODE = {
  GRAIN: "016", GSG: "016",
  SILAGE: "026", GSS: "026",
  "NO TYPE SPECIFIED": "997", NTS: "997",
  WINTER: "011", W: "011",          // wheat winter (barley winter = 091, has its own suffix)
  SPRING: "017",                    // oats spring
  REDS: "170", YELLOWS: "190",      // onions
};
// ⚠ THE SAME TYPE LETTER MEANS DIFFERENT CODES ON DIFFERENT CROPS, AND THE TABLE ABOVE
// ALREADY SAID SO WHILE THE CODE IGNORED IT. `W` is what both the wheat and the barley
// worksheet print for a winter type, and `_TYPE_NAME_TO_CODE` maps it to WHEAT's 011 —
// its own comment reads "barley winter = 091, has its own suffix". `normalizeCropType`
// took no crop, so all 16 BARLEY/W rows in pp_eligibility resolved to 011.
//
// Measured on the live book 2026-09-28, both directions: type_code 011 appears on
// TRITICALE and WHEAT and on no barley row anywhere, while 091 appears on BARLEY (39
// rows) and SOYBEANS. So the mapping produced a code that crop never carries.
//
// Only crops where a letter genuinely collides go here. Everything else stays on the
// shared table, and an unrecognised type still renders RAW rather than being guessed
// at — the convention the claim stage codes and the insurance option codes follow.
const _TYPE_BY_CROP = {
  BARLEY: { W: "091" },   // winter barley, not winter wheat
};
// `crop` is OPTIONAL and the answer without it is unchanged. Every existing caller
// passes one argument — including getCropPriceAndUnit's silage test, where a moved
// default would silently reprice a crop.
function normalizeCropType(t, crop) {
  if (!t) return "";
  const up = String(t).toUpperCase().trim();
  // "Grain-016" / "Reds-170" / "Winter-091" → extract numeric suffix
  const suffix = up.match(/-(\d{3})$/);
  if (suffix) return suffix[1];
  // Pure numeric "170" / "190"
  if (/^\d+$/.test(up)) return up;
  // The crop's own override wins over the shared name table, never the reverse.
  const perCrop = _TYPE_BY_CROP[String(crop || "").toUpperCase().trim()];
  if (perCrop && perCrop[up]) return perCrop[up];
  // Named form
  return _TYPE_NAME_TO_CODE[up] || up;
}

// Returns { price, unit } for a yield row, accounting for silage (GSS = $/ton) and potatoes ($/cwt)
function getCropPriceAndUnit(r, prices, cropYear) {
  // price_election IS authoritative when present — but it is almost never
  // present, and that is not a sync bug to go fix. Verified 2026-08-11 by
  // scanning every field of every record type in both live files: the M13 feed
  // does NOT carry the dollar price election anywhere. No field holds 4.62 on a
  // corn row, in P11, P14, P15 or anything else. The AIP prints it on the SOI
  // and omits it from the data file.
  //
  // So the fallbacks below are the REAL path for essentially every row, not a
  // safety net — they are what the grower's guarantee is computed from, which is
  // why resolveRmaPrice()'s ADM feed matters and why the verified constants are
  // maintained rather than dead code. Don't "improve" this by trusting the
  // column; check that it is populated first.
  const m13Price = Number(r.price_election || r.priceElection) || 0;

  if (normalizeCropType(r.type) === "026") {  // silage
    const fallback = RMA_SILAGE_PRICES[cropYear] || RMA_SILAGE_PRICES[2026];
    return { price: m13Price || fallback, unit: "ton" };
  }
  if ((r.crop || "").toUpperCase() === "POTATOES") {
    const isOrganic = normalizePractice(r.practice) === "Organic";
    const fallback = isOrganic
      ? (prices["POTATOES_ORGANIC"] || 33.60)
      : (prices["POTATOES"] || 17.85);
    return { price: m13Price || r.price || fallback, unit: "cwt" };
  }
  if ((r.crop || "").toUpperCase() === "ONIONS") {
    // Onions are insured in cwt, like potatoes/cabbage, and RMA prices them PER
    // TYPE — Reds (170) and Yellows (190) are different money on the same farm
    // (CY Farms Orleans 2026: $25.80 vs $15.60). So this takes the SAME per-type
    // ADM path as grapes, keyed prices["ONIONS_<typeCode>"] (commodity 0013, see
    // the per-type hydration in loadData).
    //
    // The old note here said a price "cannot be resolved" because our rows had no
    // type_code. That was true of the DATA, not of onions — the types are stated
    // on the AIP's own Schedule of Insurance and are now stored. Still no
    // hardcoded fallback: 0 renders "—" rather than a made-up number.
    const tc = r.typeCode ?? r.type_code ?? null;
    const maxPrice = (tc != null ? prices[`ONIONS_${tc}`] : null) ?? prices["ONIONS"] ?? 0;
    const pct = Number(r.priceElectionPct ?? r.price_election_pct);
    const elected = maxPrice > 0 && pct > 0 && pct <= 1 ? maxPrice * pct : maxPrice;
    return { price: m13Price || r.price || elected || 0, unit: "cwt" };
  }
  if ((r.crop || "").toUpperCase() === "CABBAGE") {
    // Cabbage RMA pricing has 4 sub-types (2026 NY): Fresh/Processing × Conv/Organic.
    // Disambiguate by row.type (M13 codes "FR"/"PROC" or full names) and practice.
    // Default to Fresh Conventional ($24.55/cwt) when nothing distinguishes it.
    const isOrganic = normalizePractice(r.practice) === "Organic";
    const typeUp = (r.type || "").toUpperCase();
    const isProcessing = typeUp.includes("PROC") || typeUp === "P";
    const variantKey = `CABBAGE_${isProcessing ? "PROC" : "FRESH"}_${isOrganic ? "ORGANIC" : "CONVENTIONAL"}`;
    const variantPrice = prices[variantKey];
    return { price: m13Price || r.price || variantPrice || prices["CABBAGE"] || 0, unit: "cwt" };
  }
  const cropUp = (r.crop || "").toUpperCase();
  if (cropUp === "DRY BEANS") {
    // Dry beans: yield in lbs/acre, price in $/cwt
    // Revenue guarantee = yield(lbs) × price($/cwt) ÷ 100 × coverageLevel
    // The ÷100 is applied at the gpa calculation site via (unit === "lbs" ? 0.01 : 1)
    const beanType = (r.type || "").toUpperCase();
    const isOrganic = normalizePractice(r.practice) === "Organic";
    const typeKey = isOrganic && beanType ? `${beanType}_ORGANIC` : beanType;
    const typePrice = typeKey && prices[typeKey];
    // Final fallback uses BTS — the most common variety and the only sensible
    // default in $/cwt. Never fall back to prices["DRY BEANS"]: that key has
    // no canonical unit (the Price screen only edits per-type BTS/DRK/…) and
    // a stale $/lb value there silently corrupts the calc by 100×.
    const fallback  = prices["BTS"] || prices["DRYBEAN_BTS"] || 0;
    return { price: m13Price || typePrice || fallback, unit: "lbs" };
  }
  if (cropUp === "PROCESSING BEANS") {
    // Processing beans: yield in tons, price in $/ton — contract price or M13
    return { price: m13Price || prices["PROCESSING BEANS"] || 0, unit: "ton" };
  }
  if (cropUp === "GRAPES") {
    // Grapes are insured in TONS/acre, not bushels, and RMA prices them PER VARIETY:
    // the ADM publishes a fixed $/ton price election per type code (NY 2026: type
    // 200 = $413, type 562 = $614), uniform across counties. Those land in
    // prices["GRAPES_<typeCode>"] from the rma_prices table — see the grape-price
    // hydration in loadData. A farmer electing below 100% (CAT at 55%, say) carries
    // the fraction on the coverage row, and the elected price is max × that fraction.
    const tc = r.typeCode ?? r.type_code ?? null;
    const maxPrice = (tc != null ? prices[`GRAPES_${tc}`] : null) ?? prices["GRAPES"] ?? 0;
    const pct = Number(r.priceElectionPct ?? r.price_election_pct);
    const elected = maxPrice > 0 && pct > 0 && pct <= 1 ? maxPrice * pct : maxPrice;
    // 0 means "unknown" — callers render "—" rather than $0.00.
    return { price: m13Price || elected || 0, unit: "ton" };
  }
  // Organic crops use a separate projected price (roughly 1.5–2x conventional)
  const practice = normalizePractice(r.practice);
  if (practice === "Organic") {
    const organicKey = (r.crop||"").toUpperCase() + "_ORGANIC";
    const organicPrice = prices[organicKey];
    if (m13Price) return { price: m13Price, unit: "bu" };
    if (organicPrice) return { price: organicPrice, unit: "bu" };
  }
  // Standard crops: M13 price_election → prices map → hardcoded RMA table (last resort).
  // The RMA fallback is what lets the bushel guarantee still compute when a caller passes
  // no prices map (e.g. the portal / Field Mapper tree) or M13 left price_election blank —
  // effectivePrice() reads the canonical RMA_SPRING_PRICES (max of projected/harvest).
  const tablePrice = prices[r.crop] || effectivePrice((r.crop || "").toUpperCase(), cropYear) || 0;
  return { price: m13Price || tablePrice, unit: "bu" };
}



// ⚠ ONE UNIT TABLE, IN src/claims/claimCrops.js. This function kept its own copy
// until 2026-09-30, which is how a potato could read cwt on one screen and bushels
// on another — the drift this repo has already recorded for the news handlers, the
// two guarantee engines and three copies of normFarmerName. It still normalises the
// TYPE here, because `normalizeCropType` is App.jsx's own rule and the shared module
// is deliberately import-free so a checker can load and drive it.
function yieldUnit(row) {
  return claimUnit((row && row.crop) || "", normalizeCropType(row && row.type));
}

// Strip NAU county designation suffixes: N=Neighboring, P=Primary, D=Designated, B=Bordering
// e.g. "Wyoming N" → "Wyoming", "Genesee P" → "Genesee"
function normalizeCounty(county) {
  if (!county) return county;
  // Strip the trailing practice indicator (" N"/" P"/etc.) and canonicalize to
  // UPPER case so "Monroe" and "MONROE" collapse to one (counties display upper
  // app-wide). Without the upper-casing, filter dropdowns showed case-dup counties.
  return county.replace(/\s+[NPDB]$/i, "").trim().toUpperCase();
}

// Card identity for a policy number, with the crop year stripped.
// NAU numbers are state-agency-policy-cropyear (NY-944-1011047-26), so the same
// policy in two crop years looks like two policies and split into two farmer
// cards — one of them empty until acreage is reported. R&H (MP-0930443) embeds
// no year and never had the problem. Anchored to the NAU shape specifically so
// R&H / ProAg / Sompo / hand-entered numbers pass through untouched.
function normalizePolicyCardKey(policyNumber) {
  return (policyNumber || "").trim().toLowerCase().replace(/^([a-z]{2}-\d{3}-\d+)-\d{1,2}$/, "$1");
}

const fmt   = (n, d = 0) => (n == null || n === "") ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtD  = (n) => n == null ? "—" : "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// ⚠ A COUNTY IS NOT ALWAYS ONE WORD. `c.charAt(0) + c.slice(1).toLowerCase()`
// title-cases the first letter and lowercases everything after it, so the book’s
// real multi-word counties render as "Van buren", "Van wert" and
// "Orange-rockland" — on screen and on the printed wheat quote, which calls
// capCty(). Split on spaces and hyphens; deliberately NOT on apostrophes, so
// Maryland’s PRINCE GEORGE’S stays "Prince George’s" rather than becoming
// "Prince George’S".
function titleCaseName(s) {
  const t = String(s == null ? "" : s);
  if (!t) return "";
  return t.toLowerCase().replace(/(^|[\s-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase());
}

// ⚠ TWO FEEDS SPELL A COUNTY DIFFERENTLY AND BOTH REACH THE SAME KEY.
// Measured on the live tables, and it is exactly the period after ST:
//
//   arc_co_county   (ARC/PLC)   ST. LAWRENCE   ST. MARY'S
//   sco_eco_county  (SCO/ECO)   ST LAWRENCE    ST MARY'S
//   crop_coverage   (the book)  no periods at all, on any of 2,615 rows
//
// Two surfaces keyed on the raw name and each broke differently. The Area
// Coverage Tracker unions the two feeds, so its picker listed ONE county twice
// and each spelling answered half the page — pick "St Lawrence" and the
// ARC-CO vs PLC comparison has no data, pick "St. Lawrence" and SCO/ECO does
// not, and neither says the other half exists. The PRINTED wheat quote joins
// the book to arc_co_county, so its ARC/PLC page would `.filter(Boolean)` the
// county straight off the document — the "the printed quote dropped a county"
// failure this file already records twice.
//
// NY ST LAWRENCE and MD ST MARY'S are the only two affected, and no farm in
// the book insures in either today — latent, not live.
//
// ⚠ PERIODS AND WHITESPACE ONLY. An apostrophe is NOT stripped: both feeds
// already agree on ST MARY'S / PRINCE GEORGE'S, and dropping it would key a
// county under a name nothing else in this app spells that way. Measured
// before shipping — within either feed alone this merges NOTHING; no two
// distinct counties collapse onto one key in any state.
//
// Written with split/join rather than a regex on purpose: the escapes in
// `/\./g` do not survive every editing path used on this file, and a `/./g`
// that slipped through replaces EVERY character — which blanks the key and
// empties the whole county picker. It did, once. There is nothing to escape
// this way.
const caCountyKey = (v) => String(v == null ? "" : v)
  .toUpperCase().split(".").join("").split(/\s+/).filter(Boolean).join(" ");

const fmtD0 = (n) => n == null ? "—" : "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ── The two acre bases on one row ────────────────────────────────────────────
// The card is PLANTED-only by design — planted acres, planted bushel guarantee,
// planted revenue guarantee — because prevent plant carries its own reduced
// guarantee on its own tab. Premium is the exception and must NOT be split: the
// grower is billed on every INSURED acre, prevent plant included.
//
// So on any row with PP the acres/guarantee/liability columns and the premium
// columns sit on DIFFERENT bases, and nothing on the row says so. Anchor Farms'
// 2026 wheat is the extreme case — 57.06 acres that are 100% prevent plant read
// "0.0 planted · $0 liability · $416 due", which is correct and unreadable.
// Returns undefined when there is no PP, so the tooltip only appears where the
// two bases actually differ.
function premiumBasisNote(row, ppAcres) {
  const pp = Math.max(0, Number(ppAcres) || 0);
  const insured = Number(row && row.acres);
  if (!(pp > 0) || !(insured > 0)) return undefined;
  return `Premium is charged on all ${fmt(insured, 1)} INSURED acres — ` +
         `${fmt(Math.max(0, insured - pp), 1)} planted + ${fmt(pp, 1)} prevent plant. ` +
         `The acres, guarantee and liability on this row are PLANTED only; ` +
         `the prevent-plant guarantee is on the Prevent Plant tab.`;
}

// AIP display registry — keep call sites consistent across pills, dropdowns, Book of Business
const AIP_INFO = {
  NAU:            { label: "NAU",            color: "#1a4a7a" },
  RAIN_AND_HAIL:  { label: "R&H",            color: "#6b4f00" },
  PROAG:          { label: "ProAg",          color: "#2a6a3a" },
  AGRI_SOMPO:     { label: "Agri Sompo",     color: "#7a2d6a" },
  GREAT_AMERICAN: { label: "Great American", color: "#5a4a7a" },
  RCIS:           { label: "RCIS",           color: "#a44a4a" },
  OTHER:          { label: "Other",          color: "#5a5a5a" },
};
const AIP_SORT_ORDER = ["NAU","RAIN_AND_HAIL","PROAG","AGRI_SOMPO","GREAT_AMERICAN","RCIS","OTHER"];
const aipLabel = (a) => (AIP_INFO[a]?.label) || a || "—";
const aipColor = (a) => (AIP_INFO[a]?.color) || "#6b4f00";

function maxAcres(pp) {
  const v1 = pp.yr2021 ?? pp.yr1 ?? 0;
  const v2 = pp.yr2022 ?? pp.yr2 ?? 0;
  const v3 = pp.yr2023 ?? pp.yr3 ?? 0;
  const v4 = pp.yr2024 ?? pp.yr4 ?? 0;
  if (pp.maxEligAcres) return Math.max(pp.maxEligAcres, v1, v2, v3, v4);
  return Math.max(v1, v2, v3, v4);
}
// Most recent year (with acres > 0) the farmer actually planted this PP crop,
// from the yr1–yr4 acreage history. Used to label a PP-eligible-but-not-insured
// crop ("last grown 2024") instead of falsely flagging it "missing APH".
function ppLastGrown(pp) {
  if (!pp) return null;
  const pairs = [[pp.yr1, pp.yr1Year], [pp.yr2, pp.yr2Year], [pp.yr3, pp.yr3Year], [pp.yr4, pp.yr4Year]]
    .filter(([ac, y]) => y != null && Number(ac) > 0)
    .sort((a, b) => b[1] - a[1]);
  return pairs.length ? { year: pairs[0][1], acres: Number(pairs[0][0]) } : null;
}
// THE FACTOR IS PASSED IN, NEVER LOOKED UP HERE (BUG-010, 2026-09-04). It used to
// call getPPFactor(crop), which is the BASE table and cannot know the grower bought
// the factor up — so every PF policy was priced five points light with arithmetic
// that looked right. Callers resolve it through ppFactorForRow() and get a
// provenance and a band with it; a bare number reaching here is a caller that has
// decided which end of that band it means.
function ppPerAcre(ay, price, cov, crop, priceElectionPct = 1, factor = null) {
  if (!ay||!price||!cov) return null;
  if (factor == null) factor = ppBaseFactor(crop);
  const lbsFactor = (crop === "DRY BEANS") ? 0.01 : 1; // yield in lbs, price in $/cwt → ÷100
  // Price-election fraction: 1.0 (100% buy-up), 0.55 (CAT), 0.5/0.75/0.8 (reduced ECO/SCO).
  // NULL → 1.0. Without it, CAT (and reduced-election) guarantees overstate by 1/pe (~1.82× for CAT).
  const pe = (priceElectionPct == null || !(priceElectionPct > 0)) ? 1 : priceElectionPct;
  // ⚠ THE ARITHMETIC IS THE ONE RULE IN src/claims/ppRate.js (2026-10-19), the same call the
  // prevent-plant engine makes per unit: guarantee quantity rounded to 0.1 first, then the
  // price. This used to multiply straight through, so the farmer card and the adjuster
  // priced one acre a few cents apart on every multi-decimal guarantee. The `pe` resolution
  // above stays HERE, at the call site, where ppRate's contract asks it to be visible.
  // Dry beans: the yield is lbs and the card's price is $/cwt, so the price is converted to
  // $/lb before it meets the shared rule (identical to the old ÷100 on the product).
  return ppDollarsPerAcre(ay, cov, factor, price * lbsFactor, pe);
}

// Grain (016) and silage (026) corn are different products in different units
// (bu vs tons). A silage PP row must never borrow a grain APH yield: priced at the
// silage $/ton rate, a grain bushel yield produces a ~5× inflated PP guarantee
// (Anchor Farms Corn GSS: 177 bu × $38.25 × .75 × .55 = $2,792/ac). Returns true
// only when the PP row and the linked APH sit in the same unit system.
function ppAyUnitCompatible(pp, ay) {
  if (!ay) return false;
  return (normalizeCropType(pp?.type) === "026") === (normalizeCropType(ay?.type) === "026");
}

// Kevin's rule: corn SILAGE (GSS / type 026) surfaces on a farmer's card ONLY when a
// real silage APH is on file — an approved yield from the M13 APH database, either on
// the PP row itself (pp.aphYield) or a type-026 approved-yield row (ay). A bare
// PP-eligibility shell with no approved APH (stale import, e.g. a 0-acre GSS row) must
// not show a phantom GSS entry. Non-silage rows always pass.
function silageHasApprovedAph(isSilage, ppAph, ay) {
  if (!isSilage) return true;
  if (ppAph != null) return true;
  return !!(ay && ay.approvedYield != null && ay.approvedYield > 0);
}

// WHAT PREVENT-PLANT FACTOR DOES THIS CELL ACTUALLY CARRY? One resolver, and it
// is handed everything the ladder in src/ppFactor.js can use:
//   * the AIP's OWN stated per-unit guarantee (P11 #120), off the prevent-plant
//     unit in crop_coverage_units — a reading, so it outranks every model below it;
//   * the policy state/county/practice, for RMA's published CY2026 wheat table;
//   * the elected options AND whether they were ever captured at all.
// `units` is the cell's crop_coverage_units array (unitsByKey[cuKey(...)]) and may
// be undefined — the ladder simply falls further, which is the honest outcome for
// a Rain & Hail policy, since R&H sends no #120 block at all.
function ppFactorForRow(pp, ay, cropYear, units) {
  const u = (units || []).find(x => x && x.is_prevent_plant
    && x.acre_guarantee_qty != null && Number(x.aph_yield) > 0);
  return resolvePpFactor({
    crop: pp.crop,
    cropYear: cropYear != null ? cropYear : (ay && ay.cropYear) || null,
    state: (ay && ay.state) || null,
    county: pp.county || (ay && ay.county) || null,
    practiceCode: (ay && ay.practiceCode) || pp.practiceCode || null,
    options: ay ? (ay.insuranceOptions || null) : null,
    optionsCaptured: !!(ay && ay.optionsCaptured),
    acreGuaranteeQty: u ? u.acre_guarantee_qty : null,
    aphYield: u ? u.aph_yield : null,
    // crop_coverage_units carries no coverage_level; the units of one cell share
    // the coverage's, which is what the guarantee quantity was struck against.
    coverageLevel: u ? ((ay && ay.coverageLevel) != null ? ay.coverageLevel : null) : null,
  });
}

// PP per-acre from a pp row, using correct price (silage-aware, potato-aware).
// Returns a BAND, not a number: where the buy-up election is elected-but-untiered
// or never captured, the base is a genuine FLOOR (RMA sells a buy-up and no
// buy-down) and the honest reading is "at least this much". A caller that prints
// `perAcre` alone is printing the floor, which is the safe half — but it must say
// so, and ppFactorLabel/exact are what let it.
// ⚠ TWO CROP YEARS MUST NEVER MEET INSIDE ONE GUARANTEE ───────────────────
// Kevin, 2026-09-15, stating the rule: "we cannot use $7.54 and 2026 APPROVED APH
// yield to formulate a guarantee, etc etc. that must never happen."
//
// It is not enough to be careful, because this exact class has now been fixed by
// hand FOUR times in the card loader alone. So the guarantee REFUSES rather than
// trusts its caller — the same shape as assertUniformKeys(), which refuses a bulk
// write before the delete instead of hoping every row agrees.
//
// Every input that KNOWS its crop year declares it. An input that does not know
// (a price map from before this change, a row loaded by an older path) declares
// nothing and cannot contaminate a comparison it is not part of — the guard fires
// on DISAGREEMENT, never on silence, so it can be added without blanking surfaces
// that are already internally consistent.
// ⚠ BUG-081. The first version of this guard fired only when two inputs EXPLICITLY
// disagreed, and ignored an input that carried no crop year at all — "silence is
// not disagreement". That is too permissive for an insurance guarantee, and the
// mutation suite proved the weaker behaviour rather than the invariant.
//
// Kevin: "If PP eligibility says CY2027 but the APH/yield, price, or another
// year-dependent input has no year attached, Silas cannot prove those values
// belong together. That should remain UNKNOWN/PENDING."
//
// So a year-dependent input must PROVE it belongs to the asserted year. Absent is
// fine — an input that is not there cannot contaminate anything, and the priority
// ladder below already handles a missing value. PRESENT BUT YEARLESS is refused.
//
// A year must also be PLAUSIBLE. Number(null) is 0 and finite, and a coverage level
// (0.75) or a FIPS code arriving where a year belongs would otherwise be adopted as
// one — the coercion trap check-sco-eco-bands records.
// ⚠ A SPREAD DROPS THE TAG. The price map declares its crop year on a
// NON-ENUMERABLE property, because every consumer walks this object as crop -> price
// and an extra enumerable key would read as a crop. That also means `{...prices}`
// silently strips the provenance — which would refuse every guarantee on the PDF a
// grower actually holds. Re-spread through here, never inline.
function repriceMap(prices, extra) {
  const m = { ...(prices || {}), ...(extra || {}) };
  const y = prices && prices._cropYear;
  if (y != null) Object.defineProperty(m, "_cropYear", { value: y, enumerable: false });
  return m;
}

const CROP_YEAR_MIN = 2000, CROP_YEAR_MAX = 2100;
function declaredCropYear(v) {
  if (v == null) return undefined;                       // absent: nothing to prove
  if (Array.isArray(v)) {
    if (!v.length) return undefined;                     // an empty set asserts nothing
    const ys = v.map(declaredCropYear);
    if (ys.some((y) => y === null)) return null;         // any member yearless -> unproven
    const set = new Set(ys.filter((y) => y !== undefined));
    if (!set.size) return undefined;
    return set.size === 1 ? [...set][0] : null;          // members disagreeing is unproven
  }
  const raw = typeof v === "object" ? (v.cropYear ?? v._cropYear ?? v.crop_year) : v;
  const n = Number(raw);
  if (raw == null || !Number.isFinite(n)) return null;   // present but yearless
  if (n < CROP_YEAR_MIN || n > CROP_YEAR_MAX) return null;
  return n;
}

// Returns null when every present year-dependent input provably belongs to the
// asserted year, and otherwise the reason it cannot be proven.
function cropYearProvenance(assertedYear, inputs) {
  const years = new Set();
  const unproven = [];
  const a = Number(assertedYear);
  if (assertedYear != null && Number.isFinite(a) && a >= CROP_YEAR_MIN && a <= CROP_YEAR_MAX) years.add(a);
  else if (assertedYear != null) unproven.push("crop year");
  for (const [name, val] of inputs) {
    const y = declaredCropYear(val);
    if (y === undefined) continue;                       // absent
    if (y === null) { unproven.push(name); continue; }   // present, cannot prove
    years.add(y);
  }
  if (years.size > 1) return { reason: "mixed_crop_year", years: [...years].sort(), inputs: [] };
  if (unproven.length) return { reason: "unproven_crop_year", years: [...years], inputs: unproven };
  if (!years.size) return { reason: "unproven_crop_year", years: [], inputs: ["crop year"] };
  return null;
}

function ppPerAcreForRow(pp, ay, prices, cropYear, units) {
  // ⚠ REFUSE BEFORE COMPUTING. A dollar figure built from two crop years belongs
  // to neither, and nothing on screen could tell a reader which half was which.
  // ⚠ EVERY YEAR-DEPENDENT INPUT MUST PROVE ITS CROP YEAR. `units` is in the list
  // because crop_coverage_units carries the acre guarantee quantity the factor is
  // read from; the crop NAME, county and practice are year-invariant and are not.
  const prov = cropYearProvenance(cropYear, [
    ["prevent-plant eligibility", pp],
    ["approved yield", ay],
    ["price", prices],
    ["unit guarantee", units],
  ]);
  if (prov) {
    const why = prov.reason === "mixed_crop_year"
      ? "not computed — inputs span crop years " + prov.years.join(" and ")
      : "not computed — cannot prove " + prov.inputs.join(", ") + " belongs to CY" + (prov.years[0] ?? "?");
    return { perAcre: null, perAcreHigh: null, exact: true, refused: prov.reason,
             refusedYears: prov.years, refusedInputs: prov.inputs,
             ppf: { factor: null, low: null, high: null, exact: false, basis: "refused", note: why } };
  }
  const ppf = ppFactorForRow(pp, ay, cropYear, units);
  const band = (v) => v == null ? { perAcre: null, perAcreHigh: null, exact: true, ppf }
    : { perAcre: v, perAcreHigh: ppf.exact ? v : v * (ppf.high / ppf.low), exact: ppf.exact, ppf };
  // Priority 1: direct from PP doc. The AIP's own worksheet figure already carries
  // the elected factor, so it is exact whatever the options string says.
  if (pp.ppLiabilityPerAcre != null) {
    return { perAcre: pp.ppLiabilityPerAcre, perAcreHigh: pp.ppLiabilityPerAcre, exact: true,
      ppf: { ...ppf, factor: ppf.factor, exact: true, basis: 'pp_document',
             note: "from the AIP's own prevent-plant worksheet" } };
  }
  // Get correct price — silage uses $/ton, potatoes use $/cwt, dry beans by type
  const isSilage   = normalizeCropType(pp.type) === "026";
  const isPotatoes = (pp.crop || "").toUpperCase() === "POTATOES";
  const isDryBeans = (pp.crop || "").toUpperCase() === "DRY BEANS";
  let price;
  if (isSilage) {
    price = RMA_SILAGE_PRICES[cropYear] || RMA_SILAGE_PRICES[2026];
  } else if (isPotatoes) {
    price = ay?.price || prices["POTATOES"] || 17.85;
  } else if (isDryBeans) {
    // Use type-specific price (BTS, DRK, LRK etc) with organic variant if applicable.
    // pp_eligibility.type is often empty (SOI parser doesn't always capture bean type),
    // so fall back to the linked APH row's type, then to BTS as a sensible default.
    const beanType = ((pp.type || ay?.type) || "").toUpperCase();
    const isOrganic = normalizePractice(pp.practice) === "Organic";
    const typeKey = isOrganic && beanType ? `${beanType}_ORGANIC` : beanType;
    price = (typeKey && prices[typeKey]) || prices["BTS"] || 0;
  } else {
    price = prices[pp.crop] || 0;
  }
  // ⚠ A PRICE OF ZERO IS NOT A PRICE, AND $0.00 IS NOT A GUARANTEE. Every branch
  // above ends in `|| 0`, so a crop the table cannot price produced a confident
  // $0.00/acre rather than an absence. That is live the moment a new crop year is
  // opened: setPricesByYear MINTS a zero-filled entry for a year it has never seen
  // ({ CORN:0, SOYBEANS:0, "DRY BEANS":0, WHEAT:0 }), which declares perfectly good
  // provenance for that year and would have priced a whole book at zero. Refuse.
  // ⚠ A PRICE UNDER DISPUTE CANNOT UNDERWRITE A GUARANTEE. If the ADM row and our
  // own discovery average disagree beyond rounding, we do not yet know which is the
  // approved number — so the figure keeps displaying with its conflict shown, and
  // everything built on it refuses until a human settles it.
  const _pxConflict = rmaPriceConflict(pp.crop, cropYear, "projected");
  if (_pxConflict) {
    return { perAcre: null, perAcreHigh: null, exact: true, refused: "price_conflict",
             refusedYears: [], refusedInputs: ["price"], conflict: _pxConflict,
             ppf: { factor: null, low: null, high: null, exact: false, basis: "refused",
                    note: "not computed — CY" + (cropYear ?? "?") + " " + (pp.crop || "crop") +
                          " price is under review: " +
                          Object.keys(_pxConflict).filter((k) => RMA_CONFLICT_PARTY[k]).map((k) => RMA_CONFLICT_PARTY[k]).join(" and ") +
                          " disagree" } };
  }
  if (!(Number(price) > 0)) {
    return { perAcre: null, perAcreHigh: null, exact: true, refused: "no_price",
             refusedYears: [], refusedInputs: ["price"],
             ppf: { factor: null, low: null, high: null, exact: false, basis: "refused",
                    note: "not computed — no published price for " + (pp.crop || "this crop") +
                          " in CY" + (cropYear ?? "?") } };
  }
  // Price election comes from the linked APH row (pp_eligibility doesn't carry it).
  // CAT = 0.55, reduced ECO/SCO = 0.5/0.75/0.8, buy-up/unknown = 1.0.
  const pe = ay?.priceElectionPct ?? 1;
  // Priority 2: calc from PP doc APH+cov
  if (pp.aphYield != null && pp.coverageLevel != null)
    return band(ppPerAcre(pp.aphYield, price, pp.coverageLevel, pp.crop, pe, ppf.low));
  // Priority 3: calc from APH/SOI yield — but only when the linked APH matches the
  // PP row's unit system (never price a grain bushel yield at the silage $/ton rate).
  if (ay && ppAyUnitCompatible(pp, ay)) return band(ppPerAcre(ay.approvedYield, price, ay.coverageLevel, pp.crop, pe, ppf.low));
  return band(null);
}

// ─────────────────────────────────────────────────────────────────────────────
// UI PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
function Card({ children, style }) {
  return <div style={{ background:"#fff", border:"1px solid #dbe6da", borderRadius:14, padding:"20px 24px", boxShadow:"0 1px 2px rgba(18,36,26,0.04)", ...style }}>{children}</div>;
}
function SectionTitle({ children, style }) {
  return <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", textTransform:"uppercase", color:"#60735f", borderBottom:"1px solid #e4ece4", paddingBottom:7, marginBottom:14, ...style }}>{children}</div>;
}
function Pill({ children, color }) {
  return <span style={{ display:"inline-block", background:color||"#2d4a2d", color:"#fff", fontSize:10, fontWeight:800, letterSpacing:"0.1em", padding:"2px 7px", borderRadius:3, textTransform:"uppercase" }}>{children}</span>;
}
function CropYearBadge({ year }) {
  return <span style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#e8f0e8", border:"1px solid #b8d0b8", color:"#2d4a2d", fontSize:11, fontWeight:800, padding:"3px 10px", borderRadius:20, letterSpacing:"0.06em" }}>📅 CROP YEAR {year}</span>;
}
// Muted "as of {date}" freshness stamp for live data feeds. Pass the latest
// data/sync timestamp (ISO string or YYYY-MM-DD, or a Date). Renders a plain,
// palette-matched line with a relative-age suffix ("· 3d ago") so staleness is
// legible at a glance without color logic. Renders nothing if no valid date —
// so a silently-failed pipeline that returns no rows shows no stamp at all.
function FreshAsOf({ date, label = "", icon = "", prefix = "as of", style = {} }) {
  if (!date) return null;
  const raw = typeof date === "string" && date.length <= 10 ? date + "T12:00:00" : date;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  // ⚠ A DATE IN THE FUTURE IS NOT "today", AND `days <= 0` CANNOT TELL THEM APART.
  // Field Progress read "newest machine data Mar 3, 2029 · today" because 8 John Deere
  // rows are dated 2029: `days` went NEGATIVE and floor(negative) <= 0, so the one line
  // whose job is to say how current a feed is asserted it was current. That was fixed at
  // the data layer (jdDataFreshness bounds its read, and counts what it excluded) — but
  // this component is shared by 14 call sites, so the SAME defect was one future-dated
  // row away on any of them. A stamp nobody can trust is worse than no stamp, so an
  // impossible date SAYS SO rather than flattering itself into the present.
  //
  // The threshold is a whole day ahead, not zero: `date` is parsed at local NOON when it
  // is a bare YYYY-MM-DD, so a legitimately same-day value already sits up to 12 hours in
  // the future and must keep reading as "today".
  const rel = days < -1 ? "dated in the future"
            : days <= 0 ? "today" : days === 1 ? "1d ago" : days + "d ago";
  const dateStr = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return (
    <span title={`Data freshness — latest ${label ? label + " " : ""}data point: ${dateStr} (${rel})`}
      style={{ fontSize: 11, color: "#60735f", whiteSpace: "nowrap", ...style }}>
      {icon ? icon + " " : ""}{label ? label + " " : ""}{prefix} {dateStr} · {rel}
    </span>
  );
}
// Latest timestamp across an array of rows for a given field (ISO/date string).
// Returns the max string value or null. Handy for feeding <FreshAsOf/>.
function latestTs(rows, field) {
  if (!Array.isArray(rows)) return null;
  return rows.reduce((m, r) => (r && r[field] && (!m || r[field] > m)) ? r[field] : m, null);
}
// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — the app's existing brand, named.
//
// Nothing here is invented. Every value is one already dominant in the file (the
// audit counted 1,294 distinct hex literals, but they cluster tightly — #1a2e1a
// appears 893 times, #60735f 855, #2d4a2d 346). The sprawl is near-duplicates
// around those anchors: #2d6a2d / #2d6a1a / #2d5a10 / #3d7a3a all sitting next to
// each other on screen. Naming the anchors gives new work one obvious value to
// reach for, and lets existing screens converge as they're touched — no big-bang
// restyle, and the brand doesn't change.
//
// Per-line-of-business colour is NOT defined here: SECTION_META already owns it
// and every coverage screen reads from it. Duplicating it would immediately drift.
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  ink:        "#1a2e1a",   // headings, primary text, sidebar ground
  inkSoft:    "#2d4a2d",   // active nav, secondary buttons
  green:      "#2d6a2d",   // primary action
  body:       "#5a6a5a",   // running text
  muted:      "#60735f",   // secondary / supporting text
  faint:      "#677167",   // uppercase labels, meta

  bg:         "#f2f6f2",   // app background
  surface:    "#ffffff",   // cards
  surfaceAlt: "#f7faf7",   // nested rows, table stripes

  line:       "#dde7dd",   // card borders
  lineSoft:   "#e4ece4",   // inner dividers
  lineStrong: "#cdd9cd",   // inputs, chips

  danger:     "#c0392b",
  warn:       "#a4700f",
  ok:         "#2d6a2d",

  serif: "'Lora','Georgia',serif",
  sans:  "'Plus Jakarta Sans','Helvetica Neue',sans-serif",
  mono:  "ui-monospace,'Cascadia Mono',Menlo,Consolas,monospace",

  r:  { sm: 6, md: 8, lg: 12, pill: 20 },
  sp: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24 },
};

// Shared primitives. Deliberately few — the file already has 29 hand-rolled
// `card` objects and 12 `th` styles; these exist so the next one isn't the 30th.
const uiCard = (pad = 16) => ({
  background: T.surface, border: `1px solid ${T.line}`, borderRadius: 14, padding: pad,
  boxShadow: "0 1px 2px rgba(18,36,26,0.04)",
});
const uiLabel = {
  fontSize: 10.5, fontWeight: 800, letterSpacing: "0.09em",
  textTransform: "uppercase", color: T.faint,
};
// Digits align in columns in the app's own face — the typewriter mono read as an
// old terminal next to everything else (2026-10-17 sweep). T.mono stays for code.
const uiNum = { fontVariantNumeric: "tabular-nums" };
// ONE page-title style. Pages had four (serif 22, serif 24, sans 20-26, with and
// without an emoji prefix), which is most of why Silas read as several apps.
const uiPageTitle = (isMobile) => ({ margin: 0, color: T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif, lineHeight: 1.2 });
// A moving mark beside a loading line, so a long wait reads as WORKING rather than
// stuck. Inline and self-contained (its own keyframes), so it drops in front of
// any existing "Loading…" text without changing that container's layout.
function UiSpinner() {
  return (
    <span aria-hidden="true" style={{ display: "inline-block", width: 12, height: 12, marginRight: 8, verticalAlign: "-1px",
      border: "2px solid #cfe0cf", borderTopColor: "#2d6a2d", borderRadius: "50%", animation: "silasSpin .8s linear infinite" }}>
      <style>{"@keyframes silasSpin { to { transform: rotate(360deg); } }"}</style>
    </span>
  );
}

// onClick is optional — every pre-existing call site omits it and renders
// exactly as before. Passing it turns the tile into a real button (Grain
// Summary's click-through into evidence, SILAS_ACTION_INTELLIGENCE.md §42:
// "make important numbers clickable into evidence").
function StatTile({ label, value, sub, isMobile, onClick }) {
  return (
    <div onClick={onClick}
      // Two to a row on a phone: a 180px basis + 148px minimum put ONE per row at
      // 390px, so four tiles were a whole screen before any table.
      style={{ ...uiCard(isMobile ? 12 : 16), flex: isMobile ? "1 1 140px" : "1 1 180px", minWidth: isMobile ? 0 : 148,
        cursor: onClick ? "pointer" : "default",
        ...(onClick ? { transition: "box-shadow 0.15s, border-color 0.15s" } : {}) }}
      onMouseEnter={onClick ? (e => { e.currentTarget.style.borderColor = T.green; e.currentTarget.style.boxShadow = "0 2px 6px rgba(45,74,45,0.12)"; }) : undefined}
      onMouseLeave={onClick ? (e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.boxShadow = "none"; }) : undefined}>
      <div style={uiLabel}>{label}{onClick && <span style={{ marginLeft: 5, color: T.faint }}>→</span>}</div>
      <div style={{ fontSize: isMobile ? (String(value).length > 10 ? 17 : 20) : 27, fontWeight: 800, marginTop: 4, color: T.ink, ...uiNum, overflowWrap: "anywhere" }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Toggle chip. `tone` optionally carries a SECTION_META entry so a coverage chip
// wears its line-of-business colour instead of generic green.
function Chip({ active, onClick, children, tone = null, title }) {
  const on = !!active;
  const fg = on ? "#fff" : (tone ? tone.color : T.inkSoft);
  const bg = on ? (tone ? tone.color : T.inkSoft) : (tone ? tone.bg : T.surfaceAlt);
  const bd = on ? (tone ? tone.color : T.inkSoft) : (tone ? tone.border : T.lineStrong);
  return (
    <button onClick={onClick} title={title}
      style={{ cursor: onClick ? "pointer" : "default", border: `1px solid ${bd}`, borderRadius: T.r.pill,
               padding: "5px 12px", fontSize: 11.5, fontWeight: 700, background: bg, color: fg,
               fontFamily: "inherit", whiteSpace: "nowrap", lineHeight: 1.45 }}>
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NAV MANIFEST — the ONE definition of the agent app's navigation.
//
// The desktop sidebar and the mobile bottom-sheet both render from this array.
// They used to be two hand-maintained trees 30,000 lines apart and had silently
// drifted: Field Progress and Model Scorecard were missing from mobile entirely,
// and Communications / Spring Prices sat in different groups on each. Adding a
// screen is now one entry here.
//
// Grouped by TASK rather than by data type. The old "Analysis & Tools" held ten
// unrelated screens and "Admin" mixed real settings with daily work, so neither
// could be scanned — you re-read every label each time.
//
// `adminOnly` items are filtered through isCurrentUserAdmin() at render.
// `primary` marks the ≤5 items that get their own mobile tab-bar slot.
// ─────────────────────────────────────────────────────────────────────────────
const NAV_MODEL = [
  { group: null, items: [
    // HOME IS THE LANDING PAGE, AND IT READS NO BOOK DATA. Dashboard is
    // untouched — it stays the agency command centre with acres, premium and
    // liability on it. What changed is only where you LAND: opening the app in
    // front of a customer or a prospect should not put the size of the book on
    // screen. See SilasHomeView / api/_home.js; tools/check-home-safe.js fails
    // the build if Home ever acquires a farmer read.
    { key: "home",      icon: "🏠", label: "Home",      primary: true },
    { key: "dashboard", icon: "📊", label: "Dashboard", primary: true },
    // Exceptions only — never a roster of farms (Kevin, 2026-10-19). Admin only: it
    // carries claim estimates. Deliberately NOT in PORTAL_PAGES, and Home is untouched.
    { key: "attention", icon: "⚑", label: "Needs attention", adminOnly: true },
  ]},
  { group: "Book", items: [
    { key: "book",   icon: "📇", label: "All Customers", short: "Customers", primary: true },
    { key: "claims", icon: "💰", label: "Claims",        primary: true },
  ]},
  { group: "Advise", items: [
    { key: "cropreview", icon: "✅",  label: "Crop Review" },
    { key: "coverage",   icon: "🛡️", label: "Coverage Advisor" },
    // ARC/PLC elections, ARC-IC, base acres and CCC-866 (2026-11-02). Agent app only — not
    // in PORTAL_PAGES. Its mark is a plain text glyph, not an emoji.
    { key: "farmprograms", icon: "▤",  label: "Farm Programs" },
    { key: "quoters",    icon: "🧮",  label: "Quoters" },
    { key: "agcalc",     icon: "📐",  label: "Ag Calculators" },
    // Reads no customer data — RMA handbook procedure only. See api/_adjuster-ask.js.
    { key: "adjuster",   icon: "📕",  label: "Ask the Adjuster" },
    { key: "yieldanalytics", icon: "🎯",  label: "Yield Analytics" },
  ]},
  { group: "Markets", items: [
    // NOT 💹 — that codepoint is "Chart Increasing with Yen" and has 円 drawn into
    // it, so at mobile tab size it reads as a text glyph rather than an icon.
    { key: "market",     icon: "📈", label: "Market Intelligence", short: "Intel", primary: true },
    // The Silas Grain Action engine also lives INSIDE Grain Marketing, as its
    // admin-only "Summary" tab (ROADMAP.md §1.2f / SILAS_ACTION_INTELLIGENCE.md
    // Milestone 2) — kept there too so a farm's contracts/COP/plan tabs still
    // sit beside its own action read.
    { key: "grain",      icon: "🌽", label: "Grain Marketing" },
    { key: "cashtargets", icon: "🎯", label: "Cash Targets" },
    // Milestone 4 — ranks every farm×crop by the SAME deterministic status
    // ladder the Summary tab uses, book-wide. Shadow/admin only, same reason.
    { key: "markets",    icon: "📊", label: "Markets" },
    { key: "map",        icon: "🗺️", label: "Market Map" },
    { key: "historical", icon: "🕰️", label: "Historical Data" },
  ]},
  { group: "Fields", items: [
    { key: "fields",         icon: "🌾", label: "Field Mapper", primary: true },
    { key: "harvesttracker", icon: "🌱", label: "Field Progress" },
    // Key stays `rainwatch` — it is persisted in sessionStorage and named in
    // tools/check-portal.js PORTAL_ROOTS. Only the label changed.
    { key: "rainwatch",      icon: "🌦️", label: "Weather" },
    // Weather RECORDS (what the weather DID at a point, for prevent plant / FSA SDRP
    // documents, freeze and drought) is Weather's admin-only Claim report tab as of
    // 2026-11-01 (Kevin: merge them). No menu row of its own; the `wxrecords` route
    // still opens that tab, and it is deliberately NOT in PORTAL_PAGES.
    // PRF Grid — its own page as of 2026-08-21 (it was a tab in the Markets
    // hub). It sits in Fields because it is a rainfall map of ground, beside
    // Weather; it is OUT of Markets because that hub is market DATA only and
    // this view reads the book. See PRFGridPage for the whole argument.
    { key: "prfgrid",        icon: "🌧️", label: "PRF Grid" },
  ]},
  { group: "Files", items: [
    { key: "fsa578",    icon: "📋", label: "File Management" },
    { key: "directory", icon: "📇", label: "Mailing Directory" },
    { key: "import",    icon: "⬆️", label: "Import Data" },
  ]},
  { group: "Settings", items: [
    { key: "alerts",      icon: "📨", label: "Communications" },
    // "Spring Prices" undersold it — the page carries fall wheat, the harvest
    // prices and the dry-bean elections too, and now tracks the ones RMA hasn't
    // announced yet.
    { key: "prices",      icon: "💲", label: "RMA Prices" },
    { key: "synclog",     icon: "📋", label: "Sync Log" },
    { key: "portaladmin", icon: "🔑", label: "Portal Access",   adminOnly: true },
    { key: "scorecard",   icon: "🔒", label: "Model Scorecard", adminOnly: true },
    // Book premium by agent / product / year. Principals only — the nav flag
    // hides it, the dispatch re-checks, and sql/009 revokes the table reads.
    { key: "premium",     icon: "🔒", label: "Premium Analytics", adminOnly: true },
  ]},
];

const NAV_GROUPS_DEFAULT_CLOSED = new Set(["Settings"]);

// Routes that still resolve but are no longer listed. `spring`/`fall` are now
// facets of the Book list; the routes stay alive so a persisted sessionStorage
// nav value (or an old deep link) doesn't land on a blank screen.
// yieldcheck → yield analytics (Yield Check retired, replaced by Yield Analytics);
// planting → Field Progress (Planting Survey rolled into the tracker).
// prfreview was its own page for part of 2026-08-24; it is a tab on the Coverage
// Advisor now. The route stays mapped rather than deleted because nav state is
// persisted in sessionStorage, so a browser that still holds it must land
// somewhere real instead of on a blank screen.
// audit → Needs attention: the Book Audit page was removed 2026-11-02 (Kevin:
// "I don't think book audit actually accomplishes anything"). The daily book
// check (api/_sentinel.js) still runs; its lines show on Needs attention.
// The old DRP and LRP pages (drp / lrp / lrpsignal / lrpquoting) were cut
// 2026-11-02: every tab on them already lived in Quoters or Markets. Each key
// lands on the hub that replaced it, and NAV_LEGACY_FOCUS below opens the tab
// that page used to open on. Their one unique control, "Settle AEVs from RMA",
// moved to Sync Log (admin only, unchanged). `prf` likewise: PRFTabbedView is
// gone and the prfgrid page replaced it. Nothing in either app sets nav to any
// of these any more — only a stale sessionStorage value can — so these lines
// are the entire compatibility story for those keys.
const NAV_LEGACY_ROUTES = { spring: "book", fall: "book", renewals: "book", yieldcheck: "yieldanalytics", planting: "harvesttracker", prf: "prfgrid",
  drp: "quoters", lrp: "markets", lrpsignal: "quoters", lrpquoting: "quoters", audit: "attention" };
// Retired key → the tab its replacement opens on. For "quoters" it is a
// QuotersHub tab key; for "markets" it is the LOB MarketsHub focuses (lrp →
// Cattle Market, what the old LRP page landed on).
const NAV_LEGACY_FOCUS = { drp: "drp", lrp: "lrp", lrpsignal: "lrpsignal", lrpquoting: "lrp" };
function navLegacyFocus(savedKey, hub) {
  return NAV_LEGACY_ROUTES[savedKey] === hub ? (NAV_LEGACY_FOCUS[savedKey] || null) : null;
}

// Sidebar nav colors are contrast-measured against the #1a2e1a sidebar, not
// picked by eye. The old inactive #4a6e4a sat at 2.50:1 — barely half the 4.5:1
// WCAG AA floor — so every page name you were NOT already on was close to
// unreadable, which is exactly what Mike reported. NAV_INK is 7.5:1 and still
// reads as clearly dimmer than the white active row, so the hierarchy survives.
// The active pill needed its own fix: #2d4a2d on #1a2e1a is 1.46:1, meaning the
// highlight itself was invisible even where the label wasn't. The inset bar is
// what actually marks the current page; the fill is only a supporting cue.
// Don't darken these back toward the background without re-running the ratios.
const NAV_INK        = "#9dc49d";   // 7.5:1 — inactive label
const NAV_INK_HOVER  = "#d6ead6";   // hover
const NAV_HEADING    = "#8fbc8f";   // 6.7:1 — group headings, at 10px not 9px
const NAV_HOVER_BG   = "#243d24";
const NAV_ACTIVE_BAR = "#7dc97d";
function NavBtn({ label, active, onClick, icon }) {
  const [hover, setHover] = useState(false);
  return <button onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
    style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", borderRadius:7, border:"none",
             background: active ? "#2d4a2d" : hover ? NAV_HOVER_BG : "transparent",
             boxShadow: active ? `inset 3px 0 0 ${NAV_ACTIVE_BAR}` : "none",
             color: active ? "#fff" : hover ? NAV_INK_HOVER : NAV_INK,
             fontWeight: active ? 700 : 600, fontSize:14, cursor:"pointer", width:"100%", textAlign:"left",
             transition:"background .12s, color .12s" }}><span>{icon}</span>{label}</button>;
}
// Collapsible sidebar section. Header toggles; children render only when open.
function NavGroup({ title, open, onToggle, children }) {
  return (
    <div style={{ marginTop:6 }}>
      <button onClick={onToggle} style={{ display:"flex", alignItems:"center", gap:6, width:"100%", background:"transparent", border:"none", cursor:"pointer", padding:"7px 8px 6px 8px" }}>
        <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:NAV_HEADING, textTransform:"uppercase", flex:1, textAlign:"left" }}>{title}</span>
        <span style={{ fontSize:10, color:NAV_HEADING, transform: open ? "rotate(90deg)" : "none", transition:"transform 0.15s", display:"inline-block" }}>▶</span>
      </button>
      {open && <div style={{ marginBottom:2 }}>{children}</div>}
    </div>
  );
}
function BottomNav({ nav, selectedId, setNav, setSelectedId }) {
  const [menuOpen, setMenuOpen] = useState(false);

  // Both lists derive from NAV_MODEL, so mobile can no longer drift from desktop.
  const visible = useMemo(
    () => NAV_MODEL.map(g => ({ ...g, items: g.items.filter(i => !i.adminOnly || isCurrentUserAdmin()) }))
                   .filter(g => g.items.length),
    []);
  const primaryItems = useMemo(() => visible.flatMap(g => g.items).filter(i => i.primary).slice(0, 5), [visible]);
  // An UNGROUPED page that is not on the bottom bar (Needs attention, beside Dashboard)
  // has no heading to sit under, so it leads the sheet untitled. Without this it was
  // on the desktop sidebar and nowhere at all on a phone (Kevin, 2026-10-19).
  const menuSections  = useMemo(() => {
    const loose = visible.filter(g => !g.group).flatMap(g => g.items).filter(i => !i.primary);
    const grouped = visible.filter(g => g.group).map(g => ({ title: g.group, items: g.items }));
    return loose.length ? [{ title: "", items: loose }, ...grouped] : grouped;
  }, [visible]);

  // Check if current nav is NOT one of the primary items (meaning it was selected from More menu)
  const primaryKeys = primaryItems.map(i => i.key);
  const isMoreActive = !primaryKeys.includes(nav) && nav !== "dashboard";

  const handleNav = (key) => {
    sessionStorage.setItem("cloverag_nav", key);
    setNav(key);
    setSelectedId(null);
    setMenuOpen(false);
  };

  return (
    <>
      {/* Overlay backdrop */}
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:199 }} />
      )}

      {/* Slide-up menu */}
      {menuOpen && (
        <div style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:200,
          background:"#1a2e1a", borderTopLeftRadius:16, borderTopRightRadius:16,
          maxHeight:"75vh", overflowY:"auto",
          boxShadow:"0 -8px 32px rgba(0,0,0,0.4)",
          paddingBottom:"env(safe-area-inset-bottom,0px)",
          animation:"slideUp 0.2s ease-out",
        }}>
          {/* Handle bar */}
          <div style={{ display:"flex", justifyContent:"center", padding:"10px 0 4px" }}>
            <div style={{ width:36, height:4, borderRadius:2, background:"#3a5a3a" }} />
          </div>

          {/* Close / title row */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 20px 12px" }}>
            <span style={{ fontSize:15, fontWeight:800, color:"#fff", letterSpacing:"0.02em" }}>All Pages</span>
            <button onClick={() => setMenuOpen(false)} style={{ background:"#2d4a2d", border:"none", borderRadius:8, padding:"6px 14px", color:"#8ad078", fontSize:13, fontWeight:700, cursor:"pointer" }}>✕ Close</button>
          </div>

          {menuSections.map(section => (
            <div key={section.title || "_loose"}>
              {section.title && <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:"#4a6e4a", textTransform:"uppercase", padding:"10px 20px 6px" }}>{section.title}</div>}
              {section.items.map(item => {
                const active = nav === item.key && !selectedId;
                return (
                  <button key={item.key} onClick={() => handleNav(item.key)}
                    style={{
                      display:"flex", alignItems:"center", gap:12, width:"100%",
                      padding:"13px 20px", border:"none", cursor:"pointer",
                      background: active ? "#2d4a2d" : "transparent",
                      color: active ? "#8ad078" : "#a0bea0",
                    }}>
                    <span style={{ fontSize:18, width:28, textAlign:"center" }}>{item.icon}</span>
                    <span style={{ fontSize:14, fontWeight: active ? 800 : 500 }}>{item.label}</span>
                    {active && <span style={{ marginLeft:"auto", fontSize:10, color:"#8ad078" }}>●</span>}
                  </button>
                );
              })}
            </div>
          ))}

          {/* Bottom padding for safe area */}
          <div style={{ height:20 }} />
        </div>
      )}

      {/* Bottom tab bar. NOT position:fixed — it's the bottom in-flow child of
          the 100dvh flex-column app shell (see Dashboard root). That's what
          keeps it glued to the visible bottom on iOS Safari without floating:
          a viewport-fixed bar gets stranded mid-screen as the URL bar resizes
          the visual viewport. Do not reintroduce position:fixed / translateZ. */}
      <div style={{ flexShrink:0, background:"#1a2e1a", borderTop:"1px solid #2d4a2d", display:"flex", zIndex:100, paddingBottom:"env(safe-area-inset-bottom,0px)" }}>
        {primaryItems.map(({ key, icon, label, short }) => {
          const active = nav === key && !selectedId;
          return (
            <button key={key} onClick={() => handleNav(key)}
              style={{ flex:1, background:"none", border:"none", padding:"10px 4px 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:3, cursor:"pointer", color: active ? "#8ad078" : "#4a6e4a" }}>
              <span style={{ fontSize:20 }}>{icon}</span>
              {/* short: six tabs share a phone's width, so "ALL CUSTOMERS" and "MARKET
                  INTELLIGENCE" wrapped to two lines and pushed the bar taller. */}
              <span style={{ fontSize:10, fontWeight: active ? 800 : 500, letterSpacing:"0.06em", textTransform:"uppercase", whiteSpace:"nowrap" }}>{short || label}</span>
            </button>
          );
        })}
        {/* More button */}
        <button onClick={() => setMenuOpen(prev => !prev)}
          style={{ flex:1, background:"none", border:"none", padding:"10px 4px 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:3, cursor:"pointer", color: menuOpen ? "#8ad078" : isMoreActive ? "#8ad078" : "#4a6e4a" }}>
          <span style={{ fontSize:20 }}>☰</span>
          <span style={{ fontSize:10, fontWeight: menuOpen || isMoreActive ? 800 : 500, letterSpacing:"0.06em", textTransform:"uppercase" }}>More</span>
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Portal "Add to Home Screen" helper. Farmers open the portal from an invite
// link and rarely know how to install it as an app. This smart banner detects
// the platform and shows only what's actionable there:
//   • Android / desktop Chromium — captures beforeinstallprompt and shows a real
//     one-tap button that fires the native install dialog.
//   • iOS Safari — shows the Share → Add to Home Screen steps (Apple has no
//     programmatic install; the user MUST do it by hand, and only in Safari).
//   • iOS in another browser, or an in-app email/social webview — where install
//     is impossible — tells them to reopen in Safari/Chrome (the #1 failure
//     mode: tapping the invite link opens Gmail's built-in browser, not Safari).
//   • Already installed (standalone display-mode) — renders nothing.
// Dismissible; the dismissal is remembered for 21 days so it isn't nagging.
const INSTALL_DISMISS_KEY  = "silas_portal_install_dismissed_at";
const INSTALL_DISMISS_DAYS = 21;

// Chrome fires `beforeinstallprompt` early in page load — often BEFORE the
// portal (and its banner) has mounted. So capture it at module scope, the
// moment this file loads, and stash it. The banner reads this on mount and also
// subscribes for a later firing. Without this, the one-tap Android button would
// silently never appear whenever the event beat the component to the DOM.
let _deferredInstallPrompt = null;
const _installPromptSubs = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();                 // suppress Chrome's own mini-infobar; we show our own UI
    _deferredInstallPrompt = e;
    _installPromptSubs.forEach((fn) => { try { fn(e); } catch { /* ignore */ } });
  });
  window.addEventListener("appinstalled", () => {
    _deferredInstallPrompt = null;
    _installPromptSubs.forEach((fn) => { try { fn(null); } catch { /* ignore */ } });
  });
}

// The iOS "Share" glyph, drawn inline so it renders identically everywhere
// (the SF-Symbol unicode codepoint only paints on Apple devices in the SF font).
function ShareGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign:"-3px", margin:"0 1px" }} aria-hidden="true">
      <path d="M12 15V3" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
    </svg>
  );
}

function PortalInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(_deferredInstallPrompt); // seed from the early module-level capture
  const [dismissed, setDismissed] = useState(true);   // start hidden; the effect decides
  const [env, setEnv] = useState({ standalone: true, ios: false, android: false, iosSafari: false, iosOther: false, androidInApp: false });

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const iosDevice = /iphone|ipad|ipod/i.test(ua) ||
      // iPadOS 13+ masquerades as desktop Mac — sniff touch to still catch it.
      (/macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
    const android = /android/i.test(ua);
    const standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    // Real iOS Safari carries a "Safari" token and none of the other-browser /
    // webview markers. Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS), the Gmail
    // app (GSA), and Facebook/Instagram/WeChat/LINE webviews do not qualify —
    // on iOS only genuine Safari can Add to Home Screen.
    const webviewMarker = /(crios|fxios|edgios|gsa|fban|fbav|instagram|micromessenger|line)/i;
    const iosSafari = iosDevice && /safari/i.test(ua) && !webviewMarker.test(ua);
    const iosOther  = iosDevice && !iosSafari;               // any non-Safari iOS browser/webview
    const androidInApp = android && webviewMarker.test(ua);  // e.g. link opened inside Gmail on Android

    let recentlyDismissed = false;
    try {
      const at = Number(localStorage.getItem(INSTALL_DISMISS_KEY) || 0);
      recentlyDismissed = at && (Date.now() - at) < INSTALL_DISMISS_DAYS * 864e5;
    } catch { /* localStorage unavailable — treat as not dismissed */ }

    setEnv({ standalone, ios: iosDevice, android, iosSafari, iosOther, androidInApp });
    setDismissed(standalone || recentlyDismissed);

    // Sync with the module-level capture (may already hold an event that fired
    // before this component mounted), and subscribe for a later firing/install.
    setDeferredPrompt(_deferredInstallPrompt);
    const sub = (e) => { setDeferredPrompt(e); if (!e) setDismissed(true); };
    _installPromptSubs.add(sub);
    return () => { _installPromptSubs.delete(sub); };
  }, []);

  if (dismissed || env.standalone) return null;

  const dismiss = () => {
    try { localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };
  const doInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* user dismissed native dialog */ }
    setDeferredPrompt(null);
    dismiss();
  };

  // On a phone this banner used to take about a third of the first screen on
  // every page until dismissed. It is a strip now: tighter padding, smaller type,
  // and a short line for the one-tap case. The iOS/Android STEPS stay verbatim -
  // they are instructions the customer has to follow, not a pitch.
  const narrow = typeof window !== "undefined" && window.innerWidth < 600;
  const btn = { border:"none", borderRadius:8, padding: narrow ? "6px 11px" : "7px 14px", fontSize: narrow ? 12.5 : 13, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap" };

  // Pick the one message that applies. deferredPrompt (Android/desktop Chromium)
  // wins because it's a genuine one-tap install; then iOS Safari steps; then the
  // "wrong browser" redirect. Anything else renders nothing.
  let body = null;
  if (deferredPrompt) {
    body = (
      <>
        <span>{narrow ? "Add SILAS to your home screen." : "Add SILAS to your home screen for one-tap access — no login link to dig up."}</span>
        <button onClick={doInstall} style={{ ...btn, background:"#a8d878", color:"#12240f" }}>{narrow ? "Add" : "Add to Home Screen"}</button>
      </>
    );
  } else if (env.iosSafari) {
    body = (
      <span>Add SILAS to your home screen: tap the <b>Share</b> button<ShareGlyph /> at the bottom of Safari, then <b>“Add to Home Screen.”</b></span>
    );
  } else if (env.iosOther || env.androidInApp) {
    const browser = env.ios ? "Safari" : "Chrome";
    body = (
      <span>To save SILAS to your home screen, first reopen this page in <b>{browser}</b> — tap the menu (<b>⋯</b> or <b>⋮</b>) and choose <b>“Open in {browser}.”</b></span>
    );
  } else if (env.android) {
    // Android in a real browser but no one-tap prompt available (event missed,
    // criteria not met, Firefox, etc.) — always give manual steps, never a blank.
    body = (
      <span>Add SILAS to your home screen: tap the <b>⋮</b> menu (top-right), then <b>“Add to Home screen.”</b></span>
    );
  } else {
    return null;   // desktop non-Chromium etc. — nothing useful to say, stay quiet
  }

  return (
    <div style={{
      display:"flex", alignItems:"center", gap: narrow ? 8 : 12, flexWrap: narrow ? "nowrap" : "wrap",
      background:"#1a2e1a", color:"#eaf3ea",
      border:"1px solid #2d4a2d", borderRadius: narrow ? 10 : 12,
      padding: narrow ? "8px 10px" : "12px 14px", marginBottom: narrow ? 12 : 16, fontSize: narrow ? 12.5 : 13.5, lineHeight:1.45,
    }}>
      <span style={{ fontSize: narrow ? 16 : 20, lineHeight:1, flexShrink:0 }} aria-hidden="true">📲</span>
      <div style={{ flex:1, minWidth: narrow ? 0 : 180, display:"flex", alignItems:"center", gap: narrow ? 8 : 10, flexWrap: "wrap", justifyContent: "space-between" }}>
        {body}
      </div>
      <button onClick={dismiss} aria-label="Dismiss"
        style={{ ...btn, background:"transparent", color:"#7aaa7a", padding:"6px 8px", fontSize:16, flexShrink:0 }}>✕</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PORTAL USAGE TELEMETRY
//
// Answers "who actually uses the portal, and for what" — the layer we were
// missing between "invited" (portal_users), "ever signed in" (auth.users) and
// "installed on their phone" (portal_installs). Writes append-only rows to
// portal_events with the farmer's own JWT (RLS pins user_id = auth.uid();
// SELECT is admin-only). See sql/007_portal_analytics.sql.
//
// Rules this module holds to:
//   • OFF unless the real farmer portal is mounted. The agent app and admin
//     "Preview as" never emit — otherwise Kevin's own clicks would drown out
//     the customer signal we're trying to read.
//   • Fire-and-forget. Queued, batched, and swallowed on failure. A telemetry
//     write must never delay a render or surface an error to a farmer.
//   • No PII in `detail`. Screen keys and verb slugs only — the user_id is the
//     join key, and portal_users already holds the name.
// ─────────────────────────────────────────────────────────────────────────────
let _ptOn      = false;   // portal mounted (not agent app, not admin preview)
let _ptSession = null;    // one id per tab-session
let _ptQueue   = [];
let _ptTimer   = null;
let _ptLastScreen = null; // current nav key, so heartbeats attribute to a screen

function _ptContext() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  let standalone = false;
  try {
    standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
  } catch { /* ignore */ }
  return {
    device: /iphone|ipad|ipod|android|mobile/i.test(ua) ? "mobile" : "desktop",
    standalone,
    app_version: APP_VERSION,
  };
}

// Campaign attribution. Emails link in with ?src=alert_lrp / ?src=digest_drp
// etc; we capture it once, strip it from the URL so it can't be bookmarked into
// every later session, and stamp it on this session's session_start.
function _ptCaptureSrc() {
  try {
    const stored = sessionStorage.getItem("silas_portal_src");
    if (stored) return stored;
    const p = new URLSearchParams(window.location.search || "");
    const src = (p.get("src") || "").slice(0, 40).replace(/[^a-z0-9_-]/gi, "");
    if (!src) return null;
    sessionStorage.setItem("silas_portal_src", src);
    p.delete("src");
    const qs = p.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    return src;
  } catch { return null; }
}

function portalTrackingStart() {
  if (!_ptSession) {
    try {
      _ptSession = sessionStorage.getItem("silas_portal_sid");
    } catch { /* ignore */ }
    if (!_ptSession) {
      _ptSession = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      try { sessionStorage.setItem("silas_portal_sid", _ptSession); } catch { /* ignore */ }
    }
  }
  _ptOn = true;
}

function portalTrackingStop() {
  _ptOn = false;
  _ptFlush(true);
}

// `keepalive` lets the request outlive the page during pagehide/tab-hide, which
// is exactly when the last events of a session are sitting in the queue.
function _ptFlush(keepalive = false) {
  if (_ptQueue.length === 0) return;
  const rows = _ptQueue;
  _ptQueue = [];
  if (_ptTimer) { clearTimeout(_ptTimer); _ptTimer = null; }
  try {
    const headers = getAuthHeaders();
    if (!headers.Authorization) return;   // no session → nothing to attribute
    fetch(`${SUPABASE_URL}/rest/v1/portal_events`, {
      method: "POST",
      headers: { ...headers, Prefer: "return=minimal" },
      body: JSON.stringify(rows),
      keepalive,
    }).catch(() => { /* telemetry never disrupts the portal */ });
  } catch { /* ignore */ }
}

function trackPortal(event_type, props = {}) {
  if (!_ptOn) return;
  const uid = getCurrentUserId();
  if (!uid || !_ptSession) return;
  _ptQueue.push({
    user_id: uid,
    session_id: _ptSession,
    event_type,
    screen: props.screen || null,
    action: props.action || null,
    detail: props.detail || {},
    ..._ptContext(),
  });
  // Batch: flush on the next tick of idle, or immediately once a few pile up.
  if (_ptQueue.length >= 8) { _ptFlush(); return; }
  if (!_ptTimer) _ptTimer = setTimeout(() => _ptFlush(), 4000);
}

// Public helper for view components. No-ops outside the portal, so it's safe to
// call from shared components that also render in the agent app.
//   trackPortalAction("set_alert", { lob: "drp" })
function trackPortalAction(action, detail = {}) {
  trackPortal("action", { action, detail, screen: detail.screen || null });
}

// ── Once per screen per session, ACROSS A RELOAD ────────────────────────────
// The dedupe used to be a module-scope Set while `_ptSession` lives in
// sessionStorage — so a tab that reloads keeps its session id and loses its
// seen-set, and the same dead end is logged again under the same session.
// Howlett's Field Mapper on 2026-08-03 is exactly that: one session
// (msd9piui-5qwralyp), two session_starts 61s apart, two `empty_state` rows for
// one screen. The panel read 3 incidents where there were 2. The seen-set now
// rides in sessionStorage beside the id it is keyed on.
const _PT_SEEN_KEY = "silas_portal_deadends";
function _ptOnce(kind, key) {
  const k = `${kind}:${key}`;
  try {
    const raw = JSON.parse(sessionStorage.getItem(_PT_SEEN_KEY) || "null");
    const seen = raw && raw.sid === _ptSession && Array.isArray(raw.keys) ? raw.keys : [];
    if (seen.includes(k)) return false;
    seen.push(k);
    sessionStorage.setItem(_PT_SEEN_KEY, JSON.stringify({ sid: _ptSession, keys: seen.slice(-60) }));
    return true;
  } catch {
    return true;   // no sessionStorage → log it rather than lose it
  }
}

// Logs a page that rendered with nothing in it. An empty state is usually OUR
// data gap, not a farmer's disinterest — a customer opening Claims three times
// and finding a blank page is a to-do for us, and it reads identically to
// healthy usage in a plain page-view count. Fires once per screen per session.
function trackPortalEmpty(screen, isEmpty) {
  if (!_ptOn || !isEmpty || !screen) return;
  if (!_ptOnce("empty", screen)) return;
  trackPortal("action", { action: "empty_state", screen, detail: { screen } });
}

// A FAILED LOAD IS NOT AN EMPTY PAGE, and until 2026-08-16 both logged as one.
//
// Every one of these screens holds its rows in state that starts null or [], and
// flips `loading` off in a `finally` — so a fetch that THROWS leaves the initial
// value in place and the view reports "nothing to show" for a customer whose
// data is sitting right there. HOWLETT FARMS INC logged `empty_state` on the
// Field Mapper twice (2026-08-03 and 2026-08-14) holding 97 CY2026
// `policy_fields` rows that their OWN JWT can read — verified both ways: 97 rows
// under `set local role authenticated` with their sub, and the identical REST
// query returning all 97. Nothing in the log could say which of the two it was,
// and the panel called it "our data gap" either way.
//
// So the failure gets its own action and carries the reason. The two need
// opposite fixes — a data gap is an onboarding to-do, a load error is a bug —
// and the same argument the JD seed-rate errors settled applies here: an error
// that cannot say WHY leaves nothing actionable behind.
function trackPortalLoadError(screen, reason) {
  if (!_ptOn || !screen) return;
  if (!_ptOnce("loaderr", screen)) return;
  const why = String((reason && reason.message) || reason || "").slice(0, 200);
  trackPortal("action", { action: "load_error", screen, detail: { screen, reason: why } });
}

// Hook form, so a view can just declare "this rendered empty".
//
// `error` IS PART OF THE CONTRACT — pass the view's load error (or an explicit
// null where the view has no load of its own). Dropping it compiles and reads
// right, and files every failed fetch as a data gap. Guarded by
// tools/check-shared-rules.js for that reason.
function usePortalEmptyState(screen, isEmpty, ready = true, error = null) {
  useEffect(() => {
    if (!ready) return;
    if (error) trackPortalLoadError(screen, error);
    else trackPortalEmpty(screen, isEmpty);
  }, [screen, isEmpty, ready, error]);
}

// Same once-per-session dedupe, for a nav key the user isn't granted.
function trackPortalBlockedNav(requested, landedOn) {
  if (!_ptOn || !requested) return;
  if (!_ptOnce("blocked", requested)) return;
  trackPortal("action", { action: "blocked_nav", screen: landedOn || null, detail: { requested } });
}

// Mounted once inside the farmer portal. Owns the session lifecycle: opens the
// session, emits a screen_view whenever the nav changes, and heartbeats once a
// minute while the tab is VISIBLE (a backgrounded tab stops, so session length
// reflects attention rather than how long a tab sat open).
function PortalUsageTracker({ screen }) {
  useEffect(() => {
    portalTrackingStart();
    const src = _ptCaptureSrc();
    trackPortal("session_start", { screen, detail: src ? { src } : {} });
    const hb = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        trackPortal("heartbeat", { screen: _ptLastScreen });
      }
    }, 60000);
    const onHide = () => { if (document.visibilityState === "hidden") _ptFlush(true); };
    const onPageHide = () => _ptFlush(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      clearInterval(hb);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      portalTrackingStop();
    };
    // Mount-once by design: `screen` is read through _ptLastScreen so a nav
    // change doesn't restart the session.
  }, []);

  useEffect(() => {
    if (!screen) return;
    _ptLastScreen = screen;
    trackPortal("screen_view", { screen });
  }, [screen]);

  return null;
}

// Records a home-screen ("standalone") launch so the Portal Access screen can
// show which farmers have actually installed the app on their phone. Fires only
// when the portal is opened from the installed icon (display-mode: standalone) —
// the reliable cross-platform install signal, since iOS emits no install event.
// `installed_at` is set once by the column default and never sent, so first-seen
// survives; we only push `last_standalone_at`. Fire-and-forget, renders nothing.
function PortalInstallTracker() {
  useEffect(() => {
    try {
      const standalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true;
      if (!standalone) return;
      const uid = getCurrentUserId();
      if (!uid) return;
      const ua = navigator.userAgent || "";
      const platform = /iphone|ipad|ipod/i.test(ua) ? "ios" : /android/i.test(ua) ? "android" : "other";
      sbUpsert("portal_installs", [{
        user_id: uid,
        last_standalone_at: new Date().toISOString(),
        platform,
        user_agent: ua.slice(0, 300),
      }], "user_id").catch(() => { /* never disrupt the portal over a telemetry write */ });
    } catch { /* ignore */ }
  }, []);
  return null;
}

// Farmer-portal bottom tab bar. Mirrors BottomNav (the agent app's) but is
// data-driven off the user's granted nav items rather than the fixed agent
// menu. Same in-flow (NOT position:fixed) placement so it stays glued to the
// visible bottom of the 100dvh flex-column portal shell on iOS Safari — a
// viewport-fixed bar gets stranded mid-screen as the URL bar resizes the
// visual viewport (see BottomNav for the full note).
function PortalBottomNav({ items = [], cur, onNav, account = null }) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Up to 5 items fit as tabs on a phone; beyond that show the first 4 + More.
  const useMore  = items.length > 5;
  const primary  = useMore ? items.slice(0, 4) : items;
  const isMoreActive = useMore && items.slice(4).some(i => i.key === cur);
  const go = (k) => { onNav(k); setMenuOpen(false); };
  // Bottom-tab labels are tiny, so they get shortened — but "first word" is the
  // wrong rule when the first word is a possessive or a category: "My Coverage"
  // came out as "MY", which names nothing. Each item can carry its own `short`;
  // the fallback drops a leading My/The and then takes the first word, so a new
  // nav entry still gets something readable without anyone remembering to set it.
  const short = (item) => item.short
    || String(item.label || "").replace(/^(My|The)\s+/i, "").split(" ")[0];

  return (
    <>
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:199 }} />
      )}
      {menuOpen && (
        <div style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:200,
          background:"#1a2e1a", borderTopLeftRadius:16, borderTopRightRadius:16,
          maxHeight:"75vh", overflowY:"auto", boxShadow:"0 -8px 32px rgba(0,0,0,0.4)",
          paddingBottom:"env(safe-area-inset-bottom,0px)",
        }}>
          <div style={{ display:"flex", justifyContent:"center", padding:"10px 0 4px" }}>
            <div style={{ width:36, height:4, borderRadius:2, background:"#3a5a3a" }} />
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 20px 12px" }}>
            <span style={{ fontSize:15, fontWeight:800, color:"#fff", letterSpacing:"0.02em" }}>Menu</span>
            <button onClick={() => setMenuOpen(false)} style={{ background:"#2d4a2d", border:"none", borderRadius:8, padding:"6px 14px", color:"#8ad078", fontSize:13, fontWeight:700, cursor:"pointer" }}>✕ Close</button>
          </div>
          {items.map(item => {
            const active = cur === item.key;
            return (
              <button key={item.key} onClick={() => go(item.key)}
                style={{ display:"flex", alignItems:"center", gap:12, width:"100%", padding:"13px 20px", border:"none", cursor:"pointer",
                  background: active ? "#2d4a2d" : "transparent", color: active ? "#8ad078" : "#a0bea0" }}>
                <span style={{ fontSize:18, width:28, textAlign:"center" }}>{item.icon}</span>
                <span style={{ fontSize:14, fontWeight: active ? 800 : 500 }}>{item.label}</span>
                {active && <span style={{ marginLeft:"auto", fontSize:10, color:"#8ad078" }}>●</span>}
              </button>
            );
          })}
          {/* Account controls live here on a phone rather than as two permanent
              buttons in the header, which they used to crowd on every page. */}
          {account && (
            <div style={{ borderTop:"1px solid #2d4a2d", margin:"8px 20px 0", padding:"14px 0 0", display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:"#6f9a6f", textTransform:"uppercase" }}>Account</div>
              {account}
            </div>
          )}
          <div style={{ height:20 }} />
        </div>
      )}
      <div style={{ flexShrink:0, background:"#1a2e1a", borderTop:"1px solid #2d4a2d", display:"flex", zIndex:100, paddingBottom:"env(safe-area-inset-bottom,0px)" }}>
        {primary.map((item) => {
          const active = cur === item.key;
          return (
            <button key={item.key} onClick={() => go(item.key)} title={item.label}
              style={{ flex:1, minWidth:0, background:"none", border:"none", padding:"9px 2px 7px", display:"flex", flexDirection:"column", alignItems:"center", gap:3, cursor:"pointer", color: active ? "#8ad078" : "#4a6e4a" }}>
              <span style={{ fontSize:19 }}>{item.icon}</span>
              <span style={{ fontSize:10.5, fontWeight: active ? 800 : 500, letterSpacing:"0.03em", textTransform:"uppercase", maxWidth:"100%", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{short(item)}</span>
            </button>
          );
        })}
        {useMore && (
          <button onClick={() => setMenuOpen(prev => !prev)}
            style={{ flex:1, minWidth:0, background:"none", border:"none", padding:"9px 2px 7px", display:"flex", flexDirection:"column", alignItems:"center", gap:3, cursor:"pointer", color: menuOpen || isMoreActive ? "#8ad078" : "#4a6e4a" }}>
            <span style={{ fontSize:19 }}>☰</span>
            <span style={{ fontSize:10.5, fontWeight: menuOpen || isMoreActive ? 800 : 500, letterSpacing:"0.03em", textTransform:"uppercase" }}>More</span>
          </button>
        )}
      </div>
    </>
  );
}
function ConfirmModal({ message, detail, onConfirm, onCancel }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div style={{ background:"#fff", borderRadius:12, padding:"32px 36px", maxWidth:440, width:"90%", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize:32, marginBottom:12, textAlign:"center" }}>⚠️</div>
        <h3 style={{ margin:"0 0 10px", fontSize:17, color:"#1a2e1a", textAlign:"center" }}>{message}</h3>
        <p style={{ margin:"0 0 24px", fontSize:13, color:"#60735f", textAlign:"center", lineHeight:1.7 }}>{detail}</p>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onCancel} style={{ flex:1, padding:"11px", border:"1px solid #dde5dd", borderRadius:7, background:"#fff", color:"#4a6e4a", fontWeight:700, fontSize:13, cursor:"pointer" }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, padding:"11px", border:"none", borderRadius:7, background:"#c0392b", color:"#fff", fontWeight:800, fontSize:13, cursor:"pointer" }}>Yes, Replace</button>
        </div>
      </div>
    </div>
  );
}

const TYPE_META = {
  crop: { label: "Crop",  color: "#3a6e32", bg: "#eef6ec", icon: "🌽" },
  lrp:  { label: "LRP",   color: "#7a3b10", bg: "#fdf1e8", icon: "🐄" },
  drp:  { label: "DRP",   color: "#1a4a7a", bg: "#e8f0fa", icon: "🥛" },
};
function TypeBadge({ type }) {
  const m = TYPE_META[type] || {};
  return <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:m.bg, color:m.color, border:`1px solid ${m.color}33`, fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:4, letterSpacing:"0.08em" }}>{m.icon} {m.label}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// PRF VIEW
// ─────────────────────────────────────────────────────────────────────────────
const PRF_INTERVAL_LABELS = {
  JANFEB:"Jan–Feb", FEBMAR:"Feb–Mar", MARAPR:"Mar–Apr", APRMAY:"Apr–May",
  MAYJUN:"May–Jun", JUNJUL:"Jun–Jul", JULAUG:"Jul–Aug", AUGSEP:"Aug–Sep",
  SEPOCT:"Sep–Oct", OCTNOV:"Oct–Nov", NOVDEC:"Nov–Dec",
};
const PRF_INTERVAL_SEASON = {
  JANFEB:"winter", FEBMAR:"winter", MARAPR:"spring", APRMAY:"spring",
  MAYJUN:"spring", JUNJUL:"summer", JULAUG:"summer", AUGSEP:"summer",
  SEPOCT:"fall",   OCTNOV:"fall",   NOVDEC:"winter",
};
const PRF_SEASON_COLOR = {
  winter:{bg:"#e8eaf0",color:"#3a3a7a"}, spring:{bg:"#eaf0e8",color:"#2d5a2d"},
  summer:{bg:"#fff8e8",color:"#7a5a00"}, fall:  {bg:"#f0ede8",color:"#7a5a2d"},
};

// RMA numeric interval codes → text. Two encodings appear in the book (5xx and
// 6xx, same offsets). Grazing-practice intervals arrive numeric (e.g. 628) while
// haying arrives as text (APRMAY); without this map the numeric ones strip to ""
// and render as raw "628" with no rain-index match.
const PRF_NUMERIC_INTERVAL = {
  "525":"JANFEB","526":"FEBMAR","527":"MARAPR","528":"APRMAY","529":"MAYJUN","530":"JUNJUL",
  "531":"JULAUG","532":"AUGSEP","533":"SEPOCT","534":"OCTNOV","535":"NOVDEC","536":"DECJAN",
  "625":"JANFEB","626":"FEBMAR","627":"MARAPR","628":"APRMAY","629":"MAYJUN","630":"JUNJUL",
  "631":"JULAUG","632":"AUGSEP","633":"SEPOCT","634":"OCTNOV","635":"NOVDEC","636":"DECJAN",
};
// Parse raw intervalCode strings like "MAYJUNNIRR BU" → "MAYJUN", or "628" → "APRMAY"
function parsePRFCode(raw) {
  const s = (raw == null ? "" : String(raw)).trim().toUpperCase();
  const num = s.match(/^(\d{3})\b/);
  if (num && PRF_NUMERIC_INTERVAL[num[1]]) return PRF_NUMERIC_INTERVAL[num[1]];
  return s.replace(/NIR(R)?/g,"").replace(/IRR/g,"").replace(/\s*BU\s*/g,"")
    .replace(/[^A-Z]/g,"").trim().slice(0,6);
}

// ⚠ THE STORED RAINFALL IS PREFERRED BECAUSE IT IS MORE PRECISE, NOT BECAUSE IT IS
// MORE AUTHORITATIVE — and on 2026-09-01 that distinction became a wrong number on
// every PRF customer's screen.
//
// The INDEX is what RMA settles on. The inches come from a separate service
// (`?mode=rainfall`, the PRF Decision Support API), and that service changed its
// measurement unit from 0.1 mm to whole millimetres, so every MAY-JUN 2026 grid was
// written at a TENTH of the real rainfall. Diamond D Farming read:
//
//     MAY-JUN   0.59" (95%)   FINAL   Normal: 6.29"   ✓ Above trigger
//
// The index right, the trigger right, the caveat right — only the headline figure
// wrong, which is exactly why nobody could see it. A number that is wrong while
// everything around it is right does not look wrong.
//
// So a stored figure that CONTRADICTS the index on its own row is not shown: the
// index wins and the inches are derived from it, which was already the documented
// fallback. THREE surfaces render this (the coverage card's index map, the per-grid
// interval detail, and the PRF Grid page) and each had its own copy of the
// preference, so the rule lives here once — a rule defined three times is a rule
// that drifts twice, which this file records for the news handlers and the
// guarantee engines.
//
// Thresholds measured over the 25,806 settled rows carrying inches + index + normal:
// the worst LEGITIMATE disagreement is 16% / 1.45" (RMA publishes the index rounded)
// and the unit defect sits at 90%. Requiring BOTH half an inch and a quarter of the
// value flags 0 of 25,806 correct rows and all 260 checkable bad ones. Kept in step
// with DST_BAD_ABS / DST_BAD_REL in api/_prf-index.js by check-prf-rainfall-unit.
const PRF_INCH_BAD_ABS = 0.5;
const PRF_INCH_BAD_REL = 0.25;
function prfInchesContradict(stored, indexPct, normalInches) {
  const a = Number(stored), idx = Number(indexPct), nrm = Number(normalInches);
  if (!Number.isFinite(a) || !Number.isFinite(idx) || !Number.isFinite(nrm) || nrm <= 0) return false;
  const recon = (idx / 100) * nrm;
  const diff = Math.abs(a - recon), scale = Math.max(a, recon);
  return scale > 0 && diff > PRF_INCH_BAD_ABS && diff / scale > PRF_INCH_BAD_REL;
}
// The inches to SHOW for a settled interval: the published figure when it agrees
// with the index beside it, else the index's own reconstruction, else nothing.
// Returns null rather than a guess — a blank is recoverable, a wrong rainfall is not.
function prfFinalInches(stored, indexPct, normalInches) {
  const s = stored == null ? null : Number(stored);
  const derived = (indexPct != null && normalInches != null && Number(normalInches) > 0)
    ? (Number(indexPct) / 100) * Number(normalInches) : null;
  if (s != null && !prfInchesContradict(s, indexPct, normalInches)) return s;
  return derived;
}

// PRF practice (Haying vs Grazing) for one interval. There's no practice field
// in the data, but full-value coverage per acre cleanly separates them: haying
// runs ~$400-700+/ac, grazing ~$50-150/ac. full-value $/ac = liability ÷
// percent_value ÷ insured_acres (so it's stable across the interval's % share).
//
// ⚠ THE AIP'S PRACTICE CODE DECIDES IT WHEN IT IS THERE. prf_intervals.farm_unit
// holds RMA's PRF practice code on every loaded row (CY2025-2026: 5xx on haying,
// 6xx on grazing), and a haying band (425-595) is haying whatever the dollars say.
// The $250 split misreads low-value haying: CY2026 haying base values in this
// book's grids run down to $243, which at 85% and a 100% factor is $206/ac —
// "GRAZING" on a farmer's own PRF tab. The 6xx band is shared by grazing and the
// unstated type 997, so it does not decide, and the dollar test stays the fallback.
function prfPractice(iv) {
  const code = Number(iv.farmUnit ?? iv.farm_unit);
  if (Number.isInteger(code) && code >= 425 && code <= 595) return "HAYING";
  const pv = Number(iv.percentValue), ac = Number(iv.insuredAcres), liab = Number(iv.liability);
  if (pv > 0 && ac > 0 && liab > 0) return (liab / pv / ac) > 250 ? "HAYING" : "GRAZING";
  return "HAYING";
}

// ── PRF POLICIES SPAN CROP YEARS AND MUST NEVER BE SUMMED ACROSS THEM ────────
// A farmer object carries EVERY crop year's PRF policy — the loader says so at
// its own assembly site ("prfPolicies and lrpPolicies each carry EVERY crop
// year") — and one PRF policy is one crop year.
//
// ⚠ TWO YEARS MERGED IS NOT "EXTRA ROWS YOU CAN IGNORE", because the acreage
// rule sums unit lines inside each (grid, county, interval) block. That rule is
// correct and was built for Kingsley, whose Wyoming block genuinely holds
// 58.01 + 13.28 in ONE interval — so a second crop year is INDISTINGUISHABLE
// from an extra unit line and gets summed. PINGREY FARM 2, grid 27208, read
// 243.78 ac / $118,174 liability / $11,951 premium against a true CY2026
// 99.94 / $60,308 / $6,058, and its settled CY2025 policy was being priced
// against CY2026's rainfall index for a $2,138 "estimated indemnity" that never
// existed. Measured 2026-09-03: 29 of the book's PRF farms hold two crop years.
//
// The Dashboard already filtered this (inDashYear) and the farmer card did not,
// which is the whole tell — the codebase knew, one surface applied it.
//
// TWO RULES, each of which fails silently the other way:
//  • IT NARROWS, IT NEVER DELETES. priorYears comes back so every caller can
//    SAY what it set aside. A year that vanishes with nothing on screen reads as
//    a farm that dropped coverage.
//  • A POLICY WITH NO CROP YEAR IS KEPT. Positive evidence only — a NULL year is
//    not evidence the policy belongs to some other year. Measured 2026-09-03:
//    0 of 74 PRF policies carry a null crop year, so this guard moves nothing
//    today and exists for the hand-load that introduces one.
// ── ⚠ AND "THE NEWEST YEAR PRESENT" IS NOT "CURRENT COVERAGE" (2026-09-22) ───
//
// Kevin, on ANCHOR FARMS INC: *"Anchor does NOT have current PRF coverage"* — and
// Silas was showing some. Traced, and the defect is the sentence above this one.
//
// `prfCurrentYear` answered **the newest crop year the farm happens to hold**, which
// is exactly right for the job it was written for (stop summing two years into one
// figure) and is NOT a statement about coverage being in force. Every consumer read
// `.policies` as current coverage anyway. For Anchor the whole PRF footprint is:
//
//     policies      CY2025  MP-0867104   type=prf   R&H   file_last_seen_at = never
//     prf_policies  1 row,  ERIE, 85%, 105.92 ac, $60,366 liability
//     prf_intervals 2 rows, grid 27806, NOVDEC + MAYJUN, both under that CY2025 row
//     CY2026        nothing.   CY2027  nothing.
//
// `yrs = [2025]`, so `yrs.length <= 1` early-returned `{ year: 2025, policies: [it] }`
// and a settled 2025 policy rendered as the farm's current PRF.
//
// THE INVARIANT: a PRF policy for crop year X can never establish current PRF
// coverage for crop year Y. The coverage year is an ARGUMENT with no default —
// defaulting it is how "whichever year we happen to hold" became "current", and
// `scoEcoBands` already carries the same required-year rule for the same reason.
//
// BLAST RADIUS, measured over the whole book 2026-09-22: 45 farms hold a PRF policy,
// 42 hold a genuine CY2026 one, and THREE hold only a prior year — ANCHOR FARMS INC
// (ERIE, $60,366), BAINBRIDGE FARM LLC (ALLEGANY, $5,986) and MY-T ACRES INC
// (LIVINGSTON, $115,424). Those three are the whole class.
//
// ⚠ `file_last_seen_at` IS NOT THE DISCRIMINATOR, and it is worth saying why. It
// reads `never` on all three — but it reads `never` on EVERY CY2025 PRF row,
// including the 29 farms that legitimately carry current coverage, because the
// column post-dates them. Crop-year identity is what decides this; the stamp is
// corroborating lifecycle evidence and nothing more.
//
// ⚠ A FORWARD YEAR IS NOT CURRENT COVERAGE EITHER, and the data says so without
// inference. Measured the same day:
//
//     year  policies  with intervals  interval rows  $0 liability   total liability
//     2025        32              32            147             0     $4,689,612
//     2026        42              42            247             0     $7,482,977
//     2027         5               0              0             5             $0
//
// A CY2027 PRF row is a SHELL — the policy number rolled forward and nothing was
// elected or rated. So RENEWAL is decidable from the rows themselves.

// The PRF crop year in force. A PRF crop year IS a calendar year — its intervals
// run JANFEB through NOVDEC of that year — so this needs no lookup and no table.
// Passed explicitly by every caller so a surface scoped to another year can say so.
function prfCoverageYear(now) {
  const d = now instanceof Date ? now : new Date();
  return d.getFullYear();
}

// Split a farm's PRF policies into the states a screen may legitimately show.
// `coverageYear` is REQUIRED; an absent one returns a refused state rather than
// guessing, because a guess here is the defect this function exists to remove.
function prfCoverageState(prfPolicies, coverageYear) {
  const yearOf = (p) => {
    const n = Number(p && p.cropYear);
    return Number.isInteger(n) && n >= 1990 && n <= 2100 ? n : null;
  };
  const all = Array.isArray(prfPolicies) ? prfPolicies : [];
  const CY = Number(coverageYear);
  if (!(Number.isInteger(CY) && CY >= 1990 && CY <= 2100)) {
    return { coverageYear: null, year: null, policies: [], hasCurrent: false,
             unknownYear: [], hasUnknownYear: false,
             historical: all, priorYears: [], renewal: [], renewalYears: [],
             refused: "no coverage year given", all };
  }
  // ⚠ AN UNREADABLE CROP YEAR IS ITS OWN STATE — IT IS NOT CURRENT.
  // This filter used to read `yearOf(p) === null || yearOf(p) === CY`, on the
  // reasoning that a null year is not positive evidence the policy belongs to
  // some other year. That is true and it is the wrong default, because it is a
  // fail-OPEN path inside a fail-CLOSED rule: the whole premise of this function
  // is that a policy for crop year X cannot establish coverage for year Y, and a
  // policy whose year is UNKNOWN has not established that it covers Y either.
  // Treating it as current re-creates the Anchor defect — a farm told its PRF is
  // in force when nothing has shown which year the policy belongs to — through a
  // different door, on malformed or hand-loaded rows.
  //
  // So it is retained and REACHABLE (never dropped, never hidden), contributes no
  // current acreage or liability, and cannot set `hasCurrent`.
  //
  // Measured 2026-09-22: 0 of 74 PRF policies carry an unreadable crop year, so
  // this moves no farm in the live book today. It is a latent correctness fix.
  const current    = all.filter((p) => yearOf(p) === CY);
  const unknownYear = all.filter((p) => yearOf(p) === null);
  const historical = all.filter((p) => { const y = yearOf(p); return y !== null && y < CY; });
  const renewal    = all.filter((p) => { const y = yearOf(p); return y !== null && y > CY; });
  const yrsOf = (list) => [...new Set(list.map(yearOf).filter((y) => y !== null))].sort((a, b) => b - a);
  return {
    coverageYear: CY,
    // UNKNOWN never counts toward coverage in force.
    hasCurrent: current.length > 0,
    unknownYear, hasUnknownYear: unknownYear.length > 0,
    // `policies` is CURRENT coverage and nothing else. Every consumer already reads
    // this field, so narrowing it here is what makes them all correct at once.
    policies: current,
    // null when there IS no current coverage, so a caption cannot print a year the
    // farm does not carry.
    year: current.length ? CY : null,
    historical, priorYears: yrsOf(historical),
    renewal, renewalYears: yrsOf(renewal),
    refused: null, all,
  };
}


function PRFView({ farmer, isMobile }) {
  const [openGrid, setOpenGrid] = useState({});
  // The rainfall grid map, collapsed by default. Mounting it costs a state's whole
  // index history (~26k rows for Ohio, ~34k for New York), which is not something
  // to spend on every farmer card open — so it loads only when asked for.
  const [showGridMap, setShowGridMap] = useState(false);
  // Rainfall index by `${grid_id}|${interval_code}` → final_index_value (% of normal).
  // Populated from prf_grid_indices, which is fed by the RMA ADM sync. Stays
  // empty until the sync has been configured + run; UI degrades to "—".
  const [rainfallIndex, setRainfallIndex] = useState({});
  // { status: "ok" | "empty" | "error", year, message? } — drives the banner
  // that distinguishes "no index published yet" from "we asked the wrong year".
  const [indexLoadState, setIndexLoadState] = useState(null);

  // ⚠ ONE CROP YEAR ONLY. Everything below reads prfPolicies, never
  // farmer.prfPolicies — see prfCoverageState(). farmer.cropYear is the
  // all-product max and runs a year ahead once DRP rolls on July 1, so it is
  // only the fallback for a farm whose PRF rows carry no year at all.
  const prfScope = useMemo(() => prfCoverageState(farmer.prfPolicies, prfCoverageYear()), [farmer]);
  const prfPolicies = prfScope.policies;
  const prfPriorYears = prfScope.priorYears;
  const cropYearForIndex = prfScope.year != null ? prfScope.year : (farmer.cropYear || null);
  const gridIdsForFetch = useMemo(() => {
    const set = new Set();
    prfPolicies.forEach(p => (p.intervals||[]).forEach(iv => {
      const gid = iv.gridId || iv.grid_id;
      if (gid) set.add(String(gid));
    }));
    return [...set];
  }, [prfPolicies]);

  useEffect(() => {
    if (!gridIdsForFetch.length || !cropYearForIndex) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await sbFetch("prf_grid_indices", {
          select: "grid_id,interval_code,final_index_value,final_rainfall_inches,is_final,estimated_index_value,estimated_rainfall_inches,normal_rainfall_inches,normal_to_date_inches,estimated_as_of,estimated_source",
          filters: [
            `grid_id=in.(${gridIdsForFetch.join(",")})`,
            `crop_year=eq.${cropYearForIndex}`,
          ],
        });
        if (cancelled) return;
        const map = {};
        for (const r of rows || []) {
          // Stores both final (RMA, official, post-close) and estimated
          // (NOAA, running, pre-close) values. UI presents whichever exists
          // and shows both side by side when both are present.
          map[`${r.grid_id}|${r.interval_code}`] = {
            value: r.final_index_value,          // back-compat alias for the per-row column
            finalValue: r.final_index_value,
            // Guarded: a stored figure contradicting its own index is not shown.
            finalInches: prfFinalInches(r.final_rainfall_inches, r.final_index_value, r.normal_rainfall_inches),
            isFinal: r.is_final,
            estimatedValue: r.estimated_index_value,
            estimatedInches: r.estimated_rainfall_inches,
            normalInches: r.normal_rainfall_inches,
            // Normal for the days the estimate covers (sql/122) — the open-interval denominator.
            normalToDateInches: r.normal_to_date_inches,
            estimatedAsOf: r.estimated_as_of,
            estimatedSource: r.estimated_source,
          };
        }
        setRainfallIndex(map);
        // A zero-row result is the failure mode that hid the July 2026 outage
        // for three days: every interval renders "—", which is also what a
        // legitimately-unpublished interval looks like, so nothing surfaced.
        // Record it so the card can say WHY it's empty instead of shrugging.
        setIndexLoadState(Object.keys(map).length === 0
          ? { status: "empty", year: cropYearForIndex }
          : { status: "ok", year: cropYearForIndex });
      } catch (e) {
        if (cancelled) return;
        console.warn("prf_grid_indices fetch failed:", e.message);
        setIndexLoadState({ status: "error", year: cropYearForIndex, message: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [gridIdsForFetch.join(","), cropYearForIndex]);

  if (!farmer.prfPolicies?.length) {
    return <Card style={{textAlign:"center",padding:"40px",color:"#60735f"}}>No PRF policies on file.</Card>;
  }

  {/* ⚠ PRF ROWS EXIST BUT NONE IS CURRENT — a THIRD state, and the one Anchor Farms
      was in. The guard above tests `farmer.prfPolicies`, i.e. every crop year the farm
      has ever held, so a farm whose only policy is CY2025 walked straight past it into
      `prfPolicies[0]` on an empty array. Before this, the CY2025 policy was simply
      rendered as the current one.

      History stays VISIBLE and stays LABELLED. Kevin's rule: prior PRF may be shown,
      it must name its crop year, and it must never appear under current coverage. */}
  if (!prfScope.hasCurrent) {
    const money = (n) => "$" + Math.round(Number(n) || 0).toLocaleString();
    const row = (p, kind) => (
      <div key={(p.policyNumber || p.id || "") + "|" + p.cropYear} style={{
        display:"flex", flexWrap:"wrap", gap:10, alignItems:"baseline", padding:"7px 0",
        borderTop:"1px solid #edf2ed", fontSize:12.5, color:"#3a4a3a" }}>
        {/* An unreadable crop year must not render as "CYnull" — that reads as a
            broken page rather than as a row somebody needs to go and correct. */}
        <b style={{ color: kind === "prior" ? "#8a6d2b" : kind === "unknown" ? "#8a5a00" : "#5a6a7a" }}>
          {kind === "unknown" ? "Crop year not readable" : "CY" + p.cropYear}</b>
        <span>{p.policyNumber || "—"}</span>
        {p.county && <span>· {titleCaseName(String(p.county))} County</span>}
        {Number(p.totalAcres) > 0 && <span>· {Number(p.totalAcres).toFixed(2)} ac</span>}
        {Number(p.totalLiability) > 0 && <span>· {money(p.totalLiability)} liability</span>}
        {(p.intervals || []).length > 0 && <span>· {(p.intervals || []).length} interval{(p.intervals||[]).length !== 1 ? "s" : ""}</span>}
      </div>
    );
    return (
      <Card style={{ padding:"22px 24px" }}>
        <div style={{ fontSize:15, fontWeight:800, color:"#8a5a00", marginBottom:4 }}>
          No current PRF coverage for {prfScope.coverageYear}
        </div>
        <div style={{ fontSize:12, color:"#5a6a5a", lineHeight:1.5, marginBottom:14 }}>
          {farmer.name} holds no PRF policy for crop year {prfScope.coverageYear}.
          {prfScope.priorYears.length ? " A prior year is on file and is shown below as history." : ""}
          {prfScope.renewalYears.length ? " A renewal is on file and is not yet in force." : ""}
          {prfScope.hasUnknownYear ? " A policy with no readable crop year is on file and is listed below." : ""}
        </div>
        {/* ⚠ RETAINED AND REACHABLE, BUT NEVER COUNTED. A policy whose crop year
            cannot be read is not evidence of coverage for THIS year, so it does not
            set `hasCurrent` and contributes no acreage or liability — but hiding it
            would be worse than miscounting it, because then nobody could find the
            row to correct. It is named as unresolved rather than filed under a year
            nothing established. */}
        {prfScope.hasUnknownYear && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:".05em", textTransform:"uppercase",
                          color:"#8a5a00", marginBottom:2 }}>
              Crop year not readable — not counted as coverage in force
            </div>
            {prfScope.unknownYear.map((p) => row(p, "unknown"))}
          </div>
        )}
        {prfScope.historical.length > 0 && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:".05em", textTransform:"uppercase",
                          color:"#8a6d2b", marginBottom:2 }}>
              Prior PRF coverage — history, not coverage in force
            </div>
            {prfScope.historical.map((p) => row(p, "prior"))}
          </div>
        )}
        {prfScope.renewal.length > 0 && (
          <div>
            <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:".05em", textTransform:"uppercase",
                          color:"#5a6a7a", marginBottom:2 }}>
              Renewal on file — not yet in force
            </div>
            {prfScope.renewal.map((p) => row(p, "renewal"))}
          </div>
        )}
      </Card>
    );
  }

  // ── COMBINE this crop year's prfPolicies into one unified view ──
  // A farmer may have multiple prfPolicies for ONE year (multi-county split
  // imports) and we display those as a single banner. Policies from OTHER crop
  // years are excluded above and named on screen — never merged in.
  const allIntervals = prfPolicies.flatMap(p => p.intervals || []);
  const firstPolicy = prfPolicies[0];

  // ── BANNER TOTALS: compute from ALL intervals across ALL policies ──
  const totLiab        = allIntervals.reduce((s,e) => s+(Number(e.liability)||0), 0);
  const intervalPremSum = allIntervals.reduce((s,e) => s+(Number(e.premium)||0), 0);
  // Insured acres across the policy.
  //
  // PRF re-insures the SAME ground in every elected interval, so acres must not
  // sum across interval codes. But within ONE interval a (grid, county) block
  // can carry SEVERAL unit lines — Kingsley's Wyoming block is 58.01 + 13.28 —
  // and those DO sum. The old code kept only the FIRST line per grid
  // (`if (!gridAc[gid])`), which dropped every additional line and keyed on
  // gridId alone even though one grid can span two counties. Kingsley reported
  // 6.6 acres against a true 77.89.
  //
  // Sum lines inside each (grid, county, interval) → max across intervals per
  // block → sum blocks. Mirrors prfTotalAcres() in api/m13-sync.js.
  const intervalAcresSum = (() => {
    const byBlockIv = {};
    allIntervals.forEach(e => {
      const block = `${e.gridId || e.grid_id || "?"}|${(e.county || "").toUpperCase()}`;
      const key = `${block}|${e.intervalCode || "?"}`;
      byBlockIv[key] = (byBlockIv[key] || 0) + (Number(e.insuredAcres) || 0);
    });
    const byBlock = {};
    Object.entries(byBlockIv).forEach(([key, acres]) => {
      const block = key.split("|").slice(0, 2).join("|");
      byBlock[block] = Math.max(byBlock[block] || 0, acres);
    });
    return Object.values(byBlock).reduce((s, a) => s + a, 0);
  })();
  
  // Premium semantics (verified against raw M13 P11 f[32], 2026-07-02):
  //   prf_intervals.premium == M13 f[32] == the TOTAL (gross) premium, NOT net.
  //   Summed across intervals it equals prf_policies.total_premium.
  //   Net premium (what the farmer actually pays) = gross − subsidy, and is
  //   stored separately as prf_policies.net_premium. Earlier this block treated
  //   the interval sum as "net" and then added subsidy to invent a gross — that
  //   overstated BOTH figures (e.g. Lakeshore net showed $62,510 vs true $28,129,
  //   gross showed $96,891 vs true $62,510). Use the stored values.
  const totalStoredPrem = prfPolicies.reduce((s,p) => s + (Number(p.totalPremium) || 0), 0);
  const grossPrem = totalStoredPrem > 0 ? totalStoredPrem : intervalPremSum;
  // Subsidy: sum across all policies
  const totalSubsidy = prfPolicies.reduce((s,p) => s + (Number(p.subsidy) || 0), 0);
  const subsidy = totalSubsidy > 0 ? totalSubsidy : null;
  // Net = what the farmer pays. Prefer the stored net_premium; fall back to
  // gross − subsidy, then to gross when no subsidy figure is available.
  const totalStoredNet = prfPolicies.reduce((s,p) => s + (Number(p.netPremium) || 0), 0);
  const netPrem = totalStoredNet > 0 ? totalStoredNet
                : (subsidy > 0 ? grossPrem - subsidy : grossPrem);
  
  // Acres: sum stored totalAcres across policies, but validate against interval sum
  const totalStoredAcres = prfPolicies.reduce((s,p) => s + (Number(p.totalAcres) || 0), 0);
  const totalAcres = totalStoredAcres >= intervalAcresSum ? totalStoredAcres : intervalAcresSum;
  
  // Productivity is elected per line (prf_intervals.productivity_factor), so the
  // header reads the LINES: one value is that value, several read "varies"
  // rather than whichever policy loaded first.
  const pfLines = [...new Set(allIntervals.map(e => Number(e.productivityFactor)).filter(v => Number.isFinite(v) && v > 0))];
  const pf  = pfLines.length === 1 ? pfLines[0] : pfLines.length > 1 ? "varies" : firstPolicy.productivityFactor;
  const cvg = firstPolicy.coverageLevel;

  // ── PRF INDEMNITY CALC ──
  // RMA's formula: payment_factor = max(0, (coverage_level - grid_index) / coverage_level)
  // indemnity = liability × payment_factor
  // Liability already incorporates productivity factor, coverage level, acres, %
  // value share — so we DON'T multiply by those again. Returns null if the
  // grid index hasn't been published yet (so the UI can show "—" instead of $0).
  function calcIndemnity(liability, gridIndexPct, coverageLevel) {
    if (gridIndexPct == null || coverageLevel == null || liability == null) return null;
    const gi = Number(gridIndexPct) / 100;
    const cl = Number(coverageLevel);
    if (!Number.isFinite(gi) || !Number.isFinite(cl) || cl <= 0) return null;
    const paymentFactor = Math.max(0, (cl - gi) / cl);
    return Math.max(0, Number(liability) * paymentFactor);
  }

  // ⚠ OPEN INTERVALS SHOW NO DOLLARS (Kevin 2026-11-02, src/prf/prfIntervalStatus.js).
  // The running index is rain so far ÷ the WHOLE interval's normal, so mid-interval it
  // reads low by the part still to come: 58 of 139 open 2026 grid-intervals read as
  // triggers and the open-interval estimate ran 7.1× what settled. An open interval
  // shows percent of normal TO DATE (rain so far ÷ normal for the same days) and no
  // trigger call or dollars. Dollars follow only a closed interval: "Estimate at
  // close" until RMA publishes, "Final" after. The calendar is the POLICY's crop
  // year, never today's.
  function intervalElapsedFraction(code) {
    return PRF_IS.elapsedFraction(code, cropYearForIndex);
  }
  function rowReading(iv) {
    const gid = iv.gridId || iv.grid_id;
    const code = parsePRFCode(iv.intervalCode) || iv.intervalCode;
    const entry = gid ? rainfallIndex[`${gid}|${code}`] : null;
    return PRF_IS.intervalReading(code, cropYearForIndex, entry);
  }
  function rowIndemnity(iv) {
    const rd = rowReading(iv);
    const amount = rd.dollarLabel ? calcIndemnity(Number(iv.liability) || 0, rd.index, cvg) : null;
    return { primary: amount, source: rd.source, label: rd.dollarLabel, status: rd.status, reading: rd };
  }

  // Banner total — closed intervals only; open ones carry no dollars.
  const indemnityTotal = (() => {
    let any = false, sum = 0, allFinal = true;
    for (const iv of allIntervals) {
      const ri = rowIndemnity(iv);
      if (ri.primary == null) continue;
      any = true; sum += ri.primary;
      if (ri.source !== "FINAL") allFinal = false;
    }
    return any ? { sum, allFinal } : null;
  })();
  const estTotalIndemnity = indemnityTotal ? indemnityTotal.sum : null;
  const indemnityTotalLabel = indemnityTotal && !indemnityTotal.allFinal ? "EST AT CLOSE" : "INDEMNITY";

  // Group ALL intervals by gridId + county + PRACTICE. A grid can carry both
  // haying and grazing (same intervals, different acres/$ per acre); splitting
  // by practice gives each its own correctly-labeled section instead of lumping
  // them under one "HAYING" badge with a blended cov/ac.
  const gridMap = {};
  allIntervals.forEach(iv => {
    const county = iv.county || prfPolicies.find(p => (p.intervals||[]).includes(iv))?.county || "";
    const g = `${iv.gridId || "Unknown"}|${county}|${prfPractice(iv)}`;
    if (!gridMap[g]) gridMap[g] = [];
    gridMap[g].push(iv);
  });
  const grids = Object.entries(gridMap);
  // Distinct (grid, county) count — a grid is one grid regardless of how many
  // practices it carries, so header/footer counts must not count practice splits.
  const distinctGrids = new Set(grids.map(([k]) => k.split('|').slice(0, 2).join('|'))).size;

  return (
    <div>
      <div>
        {/* ── SINGLE DARK HEADER for all policies ── */}
        <div style={{background:"#1a2e1a",borderRadius:10,padding:isMobile?"16px":"22px 28px",marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.14em",color:"#4a7a4a",textTransform:"uppercase",marginBottom:6}}>
            Pasture, Rangeland & Forage · Rainfall Index
          </div>
          <h2 style={{margin:"0 0 4px",fontSize:isMobile?20:26,fontWeight:800,color:"#fff",fontFamily:"'Lora',serif"}}>
            {farmer.name}
          </h2>
          <div style={{fontSize:12,color:"#7aaa7a",marginBottom:16,display:"flex",gap:8,flexWrap:"wrap"}}>
            {firstPolicy.policyNumber && <span style={{fontVariantNumeric: "tabular-nums"}}>{firstPolicy.policyNumber}</span>}
            {farmer.insurer && <><span>·</span><span>{farmer.insurer}</span></>}
            {farmer.agent  && <><span>·</span><span>Agent: {isCurrentUserAdmin() ? farmer.agent : "Clover Ag"}</span></>}
          </div>
          {/* Stat row */}
          <div style={{display:"flex",gap:isMobile?16:32,flexWrap:"wrap",marginBottom:14}}>
            {[
              {val: totalAcres > 0 ? totalAcres.toLocaleString(undefined,{maximumFractionDigits:2})+" ac" : "—", lbl:"TOTAL ACRES"},
              {val: "$"+Number(totLiab).toLocaleString(),   lbl:"TOTAL LIABILITY"},
              {val: grossPrem !== netPrem ? "$"+Number(grossPrem).toLocaleString() : "—",   lbl:"GROSS PREMIUM"},
              {val: "$"+Number(netPrem).toLocaleString(), lbl:"NET PREMIUM"},
              {val: subsidy != null && subsidy > 0 ? "$"+Number(subsidy).toLocaleString() : "—", lbl:"SUBSIDY"},
              {val: cvg != null ? (cvg*100).toFixed(0)+"%" : "—",  lbl:"COVERAGE"},
              {val: pf === "varies" ? "varies" : pf != null ? (pf *100).toFixed(0)+"%" : "—",  lbl:"PROD FACTOR"},
              {
                val: estTotalIndemnity != null
                  ? "$"+Math.round(estTotalIndemnity).toLocaleString()
                  : "—",
                lbl: indemnityTotalLabel,
                highlight: estTotalIndemnity != null && estTotalIndemnity > 0,
              },
            ].map(s=>(
              <div key={s.lbl}>
                <div style={{fontSize:isMobile?17:21,fontWeight:800,color: s.highlight ? "#ffd479" : "#fff",lineHeight:1}}>{s.val}</div>
                <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",color: s.highlight ? "#d4a850" : "#4a7a4a",marginTop:3}}>{s.lbl}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:11,color:"#6a9a6a"}}>
            <span>{(() => {
              const counties = [...new Set(allIntervals.map(iv => (iv.county || firstPolicy.county || farmer.county || '').toUpperCase().replace(/^\s+|\s+$/g, '')).filter(Boolean))];
              return counties.length > 1
                ? counties.map(titleCaseName).join(', ') + ' Counties'
                : (counties[0] ? titleCaseName(counties[0]) + ' County' : '');
            })()} · {cropYearForIndex || farmer.cropYear}</span>
            {cvg && <span>· Trigger Index: {(cvg*100).toFixed(0)}</span>}
            {distinctGrids > 1 && <span>· {distinctGrids} Grid IDs</span>}
            {allIntervals.length > 0 && <span>· {allIntervals.length} Interval{allIntervals.length!==1?"s":""}</span>}
            {firstPolicy.billingDate && <span>· Bill Due: {firstPolicy.billingDate}</span>}
            {/* A prior crop year is SET ASIDE, never silently dropped. Merging it
                into this card is what double-counted Pingrey; hiding it with
                nothing on screen would read as a farm that dropped coverage. */}
            {prfPriorYears.length > 0 && (
              <span style={{color:"#8a6d2b"}}>
                · Also on file, not shown: CY{prfPriorYears.join(", CY")}
              </span>
            )}
            {/* A RENEWAL IS SET ASIDE TOO, AND THE SCREEN DID NOT SAY SO. The PDF
                already named it (`_prfScope.renewalYears`); the card named only
                prior years, so a farm holding next year's policy showed nothing to
                say it exists. Measured on SULLIVAN FARMS LLC, which carries CY2026
                coverage and a CY2027 renewal: the card set the renewal aside
                silently. Same rule as history — set aside, never dropped. */}
            {prfScope.renewalYears.length > 0 && (
              <span style={{color:"#5a6a7a"}}>
                · Renewal on file: CY{prfScope.renewalYears.join(", CY")} — not yet in force
              </span>
            )}
            {/* A policy whose crop year cannot be read is set aside the same way —
                named, not counted, and not filed under a year nothing established. */}
            {prfScope.hasUnknownYear && (
              <span style={{color:"#8a5a00"}}>
                · {prfScope.unknownYear.length} polic{prfScope.unknownYear.length === 1 ? "y" : "ies"} with no readable crop year, not counted
              </span>
            )}
          </div>
        </div>

        {/* ── RAINFALL INDEX BY INTERVAL ──
            Per-interval rainfall index chips, prominent under the banner so a
            farmer/agent sees "are any of my intervals below the trigger?" at
            a glance without scrolling into the per-grid tables.
            • Final = RMA Final Grid Index (paid on)
            • Est at close = closed, NOAA's full-interval index, awaiting RMA
            • So far = open: rain to date vs normal for the same days, no dollars
            • Future = not started
            One chip per distinct interval the farmer has insured; with more than
            one grid, each grid's own reading. */}
        {(() => {
          const triggerPct = cvg != null ? cvg * 100 : null;
          const intervalsByCode = {};
          const nOrNull = (v) => (v != null && isFinite(Number(v)) ? Number(v) : null);
          for (const iv of allIntervals) {
            const code = parsePRFCode(iv.intervalCode);
            const label = PRF_INTERVAL_LABELS[code] || iv.intervalCode || code;
            const gid = iv.gridId || iv.grid_id;
            if (!code) continue;
            if (!intervalsByCode[code]) {
              intervalsByCode[code] = { code, label, grids: new Set(),
                indSum: 0, indAny: false, indAllFinal: true,
                estAsOf: null, perGrid: {} };
            }
            const ic = intervalsByCode[code];
            if (gid) ic.grids.add(String(gid));
            const entry = gid ? rainfallIndex[`${gid}|${code}`] : null;
            const rd = PRF_IS.intervalReading(code, cropYearForIndex, entry);
            // Each grid's own reading, kept apart — a trigger is judged per grid, never on
            // a blend of grids (see the status line below).
            if (gid && entry && !ic.perGrid[String(gid)]) {
              const mode = rd.index != null ? "index" : (rd.toDate != null ? "toDate" : null);
              ic.perGrid[String(gid)] = { gid: String(gid), mode, source: rd.source, status: rd.status,
                val: mode === "index" ? rd.index : rd.toDate,
                ins: rd.source === "FINAL" ? nOrNull(entry.finalInches) : nOrNull(entry.estimatedInches),
                normalIn: nOrNull(entry.normalInches),
                normalToDateIn: mode === "toDate"
                  ? PRF_IS.normalToDate(code, cropYearForIndex, { normalInches: entry.normalInches,
                      normalToDateInches: entry.normalToDateInches, asOf: entry.estimatedAsOf })
                  : null,
                estVal: nOrNull(entry.estimatedValue), estIn: nOrNull(entry.estimatedInches) };
            }
            if (entry?.estimatedAsOf && (!ic.estAsOf || entry.estimatedAsOf > ic.estAsOf)) {
              ic.estAsOf = entry.estimatedAsOf;
            }
            // Dollars only for a closed interval (rd.dollarLabel), summed per grid row
            // because each grid pays on its own index and liability.
            if (rd.dollarLabel) {
              const ind = calcIndemnity(Number(iv.liability) || 0, rd.index, cvg);
              if (ind != null) {
                ic.indSum += ind; ic.indAny = true;
                if (rd.source !== "FINAL") ic.indAllFinal = false;
              }
            }
          }
          const intervalChips = Object.values(intervalsByCode).sort((a,b) => {
            const order = ["JANFEB","FEBMAR","MARAPR","APRMAY","MAYJUN","JUNJUL","JULAUG","AUGSEP","SEPOCT","OCTNOV","NOVDEC","DECJAN"];
            return order.indexOf(a.code) - order.indexOf(b.code);
          });
          if (!intervalChips.length) return null;
          // Trigger colouring only for a reading a trigger can be judged on. An open
          // interval's to-date percent is never red: it is not a trigger.
          function colorFor(val, judgeTrigger) {
            if (val == null) return { bg:"#f4f8f4", fg:"#60735f", border:"#e4ece4" };
            const belowTrigger = judgeTrigger && triggerPct != null && val < triggerPct;
            if (belowTrigger || (judgeTrigger && val < 90)) return { bg:"#fbeaea", fg:"#a02828", border:"#e8b0b0" };
            if (val < 100)                 return { bg:"#fff5e0", fg:"#8a6d2b", border:"#e8d4a0" };
            return { bg:"#eaf4e4", fg:"#1a6a3a", border:"#b8d8b8" };
          }
          return (
            <div style={{background:"#fff",border:"1px solid #e4ece4",borderRadius:8,padding:isMobile?"12px 12px":"14px 18px",marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:10}}>
                <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",color:"#4a7a4a",textTransform:"uppercase"}}>
                  Rainfall Index · % of Normal
                </div>
                {triggerPct != null && (
                  <div style={{fontSize:10,color:"#60735f"}}>
                    Trigger: <strong style={{color:"#1a2e1a"}}>{triggerPct.toFixed(0)}%</strong> · below = indemnity
                  </div>
                )}
              </div>
              {indexLoadState && indexLoadState.status !== "ok" && (
                <div style={{background:"#fbeaea",border:"1px solid #e8b0b0",borderRadius:6,
                             padding:"8px 10px",marginBottom:10,fontSize:11,color:"#a02828"}}>
                  <strong>No rainfall index data for crop year {indexLoadState.year}.</strong>{" "}
                  {indexLoadState.status === "error"
                    ? `Lookup failed: ${indexLoadState.message}`
                    : "Every interval below will read —. If this policy's intervals should have values, the index sync or the crop year is wrong — not the weather."}
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(auto-fill,minmax(130px,1fr))":"repeat(auto-fill,minmax(170px,1fr))",gap:8}}>
                {intervalChips.map(ic => {
                  const chipStatus = PRF_IS.intervalStatus(ic.code, cropYearForIndex);
                  const readings = Object.values(ic.perGrid || {}).filter((g) => g.val != null);
                  // One mode per card: if any grid can only read to date, the card reads to
                  // date, so a card never shows dollars beside a grid still filling in.
                  const mode = readings.some((g) => g.mode === "toDate") ? "toDate"
                             : readings.some((g) => g.mode === "index") ? "index" : null;
                  // ⚠ A TRIGGER IS PER GRID. Each grid pays on its own index, so a card that
                  // covers two grids cannot be judged on their average: Sparta Farms' 2026
                  // Aug-Sep read 95% "Above trigger" — grid 27210 at 79% and 27209 at 111%
                  // averaged, unweighted — beside the $1,640 that 27210's 79% earns (Kevin,
                  // 2026-11-01). With more than one grid the card shows each grid, and its
                  // colour and status follow the LOWEST grid.
                  const gridRows = readings.filter((g) => g.mode === mode).sort((a, b) => a.val - b.val);
                  const multiGrid = gridRows.length > 1;
                  const lead = gridRows[0] || null;
                  const primaryVal = lead ? lead.val : null;
                  const allFinal = mode === "index" && gridRows.every((g) => g.source === "FINAL");
                  const primaryLabel = mode === "toDate" ? "SO FAR"
                                     : mode === "index" ? (allFinal ? "FINAL" : "EST AT CLOSE") : null;
                  const primaryInches = lead ? lead.ins : null;
                  const normalShown = lead ? (mode === "toDate" ? lead.normalToDateIn : lead.normalIn) : null;
                  const judge = mode === "index";
                  const colors = colorFor(primaryVal, judge);
                  let statusLine = null;
                  const triggerPctLocal = cvg != null ? cvg * 100 : null;
                  const ivElapsed = intervalElapsedFraction(ic.code);
                  const gridsBelow = judge && multiGrid && triggerPctLocal != null ? gridRows.filter((g) => g.val < triggerPctLocal) : [];
                  if (judge && primaryVal != null && triggerPctLocal != null) {
                    if (gridsBelow.length === 1) {
                      statusLine = `⚠ Grid ${gridsBelow[0].gid} below ${triggerPctLocal.toFixed(0)}% trigger`;
                    } else if (gridsBelow.length > 1) {
                      statusLine = `⚠ ${gridsBelow.length} of ${gridRows.length} grids below ${triggerPctLocal.toFixed(0)}% trigger`;
                    } else if (primaryVal < triggerPctLocal) {
                      statusLine = `⚠ Below ${triggerPctLocal.toFixed(0)}% trigger`;
                    } else if (primaryVal < triggerPctLocal + 5) {
                      statusLine = `Just above trigger`;
                    } else {
                      statusLine = `✓ Above trigger`;
                    }
                  } else if (mode === "toDate" && chipStatus === "open" && ivElapsed != null) {
                    statusLine = `Interval ${(ivElapsed * 100).toFixed(0)}% done`;
                  } else if (mode === "toDate") {
                    statusLine = "Closed · last days coming in";
                  }
                  // Data source / freshness line
                  let sourceLine = null;
                  if (allFinal) sourceLine = "✓ RMA Final";
                  else if (mode === "index") sourceLine = "NOAA · awaiting RMA final";
                  else if (mode === "toDate") sourceLine = `NOAA${ic.estAsOf ? " · thru " + ic.estAsOf.slice(5) : ""}`;
                  else if (chipStatus === "open") sourceLine = "In progress · NOAA pending";
                  else if (chipStatus === "closed") sourceLine = "RMA publishing";
                  else sourceLine = "Future interval";
                  const indVal = ic.indAny ? ic.indSum : null;
                  return (
                    <div key={ic.code} style={{background:colors.bg,border:`1px solid ${colors.border}`,borderRadius:6,padding:isMobile?"8px 10px":"10px 12px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:6}}>
                        <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.08em",color:colors.fg,textTransform:"uppercase"}}>
                          {ic.label || ic.code}
                        </div>
                        {ic.grids.size > 0 && (
                          <div style={{fontSize:9,color:colors.fg,opacity:0.7}}>
                            {ic.grids.size === 1 ? `Grid ${[...ic.grids][0]}` : `${ic.grids.size} grids`}
                          </div>
                        )}
                      </div>
                      {/* MORE THAN ONE GRID: each grid's own reading, lowest first — never a blend. */}
                      {multiGrid && (
                        <div style={{marginTop:4}}>
                          {gridRows.map((g) => {
                            const below = judge && triggerPctLocal != null && g.val < triggerPctLocal;
                            return (
                              <div key={g.gid} style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:6,fontVariantNumeric:"tabular-nums",lineHeight:1.3}}>
                                <span style={{fontSize:10,color:colors.fg,opacity:0.8}}>Grid {g.gid}</span>
                                <span style={{fontSize:isMobile?14:16,fontWeight:800,color:below?"#a02828":colors.fg}}>
                                  {g.val.toFixed(0)}%
                                  {g.ins != null && <span style={{fontSize:10,fontWeight:600,opacity:0.8,marginLeft:4}}>{g.ins.toFixed(2)}"</span>}
                                </span>
                              </div>
                            );
                          })}
                          {primaryLabel && (
                            <div style={{display:"inline-block",marginTop:3,fontSize:9,fontWeight:800,letterSpacing:"0.06em",color:colors.fg,opacity:0.85,padding:"1px 5px",border:`1px solid ${colors.fg}33`,borderRadius:3}}>
                              {primaryLabel}
                            </div>
                          )}
                        </div>
                      )}
                      {/* ONE GRID: rain in inches (big), its percent, and the normal it is measured against */}
                      {!multiGrid && <>
                      <div style={{display:"flex",alignItems:"baseline",gap:4,marginTop:4,flexWrap:"wrap"}}>
                        <div style={{fontSize:isMobile?17:21,fontWeight:800,color:colors.fg,fontVariantNumeric: "tabular-nums",lineHeight:1.05}}>
                          {primaryInches != null ? primaryInches.toFixed(2) + '"' : "—"}
                        </div>
                        {primaryVal != null && (
                          <div style={{fontSize:isMobile?11:13,fontWeight:700,color:colors.fg,opacity:0.85,fontVariantNumeric: "tabular-nums"}}>
                            ({primaryVal.toFixed(0)}%)
                          </div>
                        )}
                        {primaryLabel && (
                          <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.06em",color:colors.fg,opacity:0.85,padding:"1px 5px",border:`1px solid ${colors.fg}33`,borderRadius:3}}>
                            {primaryLabel}
                          </div>
                        )}
                      </div>
                      {/* NORMAL — for an open interval, the normal for the same days the rain
                          covers, so the percent and the inches tell one story. */}
                      {normalShown != null ? (
                        <div style={{fontSize:10,color:colors.fg,opacity:0.85,marginTop:3,fontVariantNumeric: "tabular-nums"}}>
                          {mode === "toDate" ? "Normal so far" : "Normal"}: {normalShown.toFixed(2)}"
                          {primaryInches != null && (
                            <span style={{marginLeft:6}}>
                              ({primaryInches >= normalShown ? "+" : ""}{(primaryInches - normalShown).toFixed(2)}")
                            </span>
                          )}
                        </div>
                      ) : (primaryInches != null && (
                        <div style={{fontSize:10,color:colors.fg,opacity:0.6,marginTop:3,fontStyle:"italic"}}>
                          Normal: pending climatology load
                        </div>
                      ))}
                      </>}
                      {/* CLAIM STATUS — judged only on a closed interval */}
                      {statusLine && (
                        <div style={{fontSize:10,fontWeight:700,color:colors.fg,marginTop:4,letterSpacing:"0.02em"}}>
                          {statusLine}
                        </div>
                      )}
                      {/* INDEMNITY $ — closed intervals only */}
                      {indVal != null && indVal > 0 && (
                        <div style={{fontSize:11,fontWeight:800,color:colors.fg,marginTop:3,fontVariantNumeric: "tabular-nums"}}>
                          ${Math.round(indVal).toLocaleString()}
                          <span style={{fontSize:9,fontWeight:600,marginLeft:4,opacity:0.7,letterSpacing:"0.04em"}}>
                            {ic.indAllFinal ? "INDEMNITY" : "EST AT CLOSE"}
                          </span>
                        </div>
                      )}
                      {/* SECONDARY: NOAA's reading beside RMA Final when both exist */}
                      {!multiGrid && allFinal && lead && lead.estVal != null && (
                        <div style={{fontSize:10,color:colors.fg,opacity:0.7,marginTop:3,fontVariantNumeric: "tabular-nums"}}>
                          NOAA est: {lead.estIn != null ? lead.estIn.toFixed(2) + '" ' : ""}({lead.estVal.toFixed(0)}%)
                        </div>
                      )}
                      {/* Data source / freshness */}
                      {sourceLine && (
                        <div style={{fontSize:10,color:colors.fg,opacity:0.6,marginTop:3,letterSpacing:"0.04em"}}>
                          {sourceLine}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{fontSize:10,color:"#aaa",marginTop:10,fontStyle:"italic"}}>
                <strong>SO FAR</strong> = rain to date vs normal for the same days, no payment until it closes.
                <strong> EST AT CLOSE</strong> = closed, awaiting RMA. <strong>FINAL</strong> = RMA's index, paid on.
              </div>
            </div>
          );
        })()}

            {/* ── GRID SECTIONS ── */}
            {grids.map(([gridKey, ivs]) => {
              const [displayGridId, gridCounty, gridPractice] = gridKey.split('|');
              // Acres for this block: sum the unit lines WITHIN an interval (a
              // block can have several), then take the largest interval — never
              // sum across intervals, which would multiply the same ground by
              // the number of elected intervals. The old code read ivs[0] only,
              // on the stated assumption that "all intervals for same grid have
              // same acres"; that silently dropped every additional unit line.
              const acresByIv = {};
              ivs.forEach(e => {
                const k = e.intervalCode || "?";
                acresByIv[k] = (acresByIv[k] || 0) + (Number(e.insuredAcres) || 0);
              });
              const ivAcreValues = Object.values(acresByIv);
              const gridAcres   = ivAcreValues.length ? Math.max(...ivAcreValues) : 0;
              const gridLiab    = ivs.reduce((s,e)=>s+(Number(e.liability)||0),0);
              const gridPrem    = ivs.reduce((s,e)=>s+(Number(e.premium)||0),0);
              // Coverage per acre is a PER-INTERVAL rate, so it must divide by
              // the acres actually behind that liability — i.e. acres counted
              // once per interval. Dividing the multi-interval liability sum by
              // one interval's acres inflated this by the interval count (a
              // $285/ac block with 2 intervals displayed as "$570/ac").
              const acreIntervals = ivs.reduce((s,e)=>s+(Number(e.insuredAcres)||0),0);
              const covPerAcre  = acreIntervals > 0 ? Math.round(gridLiab / acreIntervals) : 0;
              // Practice now comes from the section key (each section is one
              // practice). Fall back to the cov/ac heuristic for any legacy path.
              const gridType    = gridPractice || ivs[0]?.type || (covPerAcre > 250 ? "HAYING" : covPerAcre > 0 ? "GRAZING" : "");
              const isOpen      = !!openGrid[gridKey];
              const pctTotal    = totLiab > 0 ? ((gridLiab/totLiab)*100).toFixed(0) : null;

              return (
                <div key={gridKey} style={{marginBottom:16,borderRadius:8,border:"1px solid #e4ece4",overflow:"hidden"}}>
                  {/* Grid header — clickable. Wraps on mobile so the
                      grid name + acres + dollar totals stack vertically
                      instead of overflowing the card. */}
                  <div
                    onClick={()=>setOpenGrid(p=>({...p,[gridKey]:!p[gridKey]}))}
                    style={{padding:isMobile?"10px 12px":"12px 16px",background:"#f4f8f4",cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}
                  >
                    <div style={{display:"flex",alignItems:"center",gap:isMobile?6:12,flexWrap:"wrap"}}>
                      <span style={{fontSize:11,fontWeight:800,letterSpacing:"0.1em",color:"#1a2e1a",textTransform:"uppercase"}}>
                        Grid {displayGridId}
                      </span>
                      <span style={{fontSize:11,color:"#60735f"}}>
                        {gridCounty || ivs[0]?.county || firstPolicy.county || farmer.county || "—"} · {gridAcres.toLocaleString(undefined,{maximumFractionDigits:2})} ac
                      </span>
                      {gridType && (
                        <span style={{fontSize:10,fontWeight:800,padding:"2px 7px",borderRadius:4,letterSpacing:"0.05em",
                          background: gridType === "HAYING" ? "#e3f0e3" : "#fef3e0",
                          color: gridType === "HAYING" ? "#2d6a2d" : "#8a6d2b",
                          border: `1px solid ${gridType === "HAYING" ? "#b8d8b8" : "#e8d4a0"}`}}>
                          {gridType}
                        </span>
                      )}
                      <span style={{fontSize:10,background:"#e8f0e8",color:"#2d5a2d",padding:"2px 8px",borderRadius:4,fontWeight:700}}>
                        {ivs.length} interval{ivs.length!==1?"s":""}
                      </span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:isMobile?8:16,flexWrap:"wrap"}}>
                      {covPerAcre > 0 && !isMobile && (
                        <span style={{fontSize:11,color:"#60735f",fontVariantNumeric: "tabular-nums"}}>
                          ${covPerAcre.toLocaleString()}/ac
                        </span>
                      )}
                      <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,color:"#1a6a3a",fontSize:isMobile?12:13}}>
                        ${gridLiab.toLocaleString()}{!isMobile ? " liability" : ""}
                      </span>
                      {pctTotal && !isMobile && <span style={{fontSize:11,color:"#60735f"}}>{pctTotal}% of total</span>}
                      <span style={{fontSize:11,color:"#aaa",transform:isOpen?"rotate(90deg)":"rotate(0deg)",display:"inline-block",transition:"transform 0.15s"}}>▶</span>
                    </div>
                  </div>

                  {/* Interval table — shown when grid is open (default open).
                      Wrapped in overflow-x:auto so the 8-column table can be
                      swiped horizontally on phones instead of overflowing the
                      farmer card. */}
                  {(isOpen || openGrid[gridKey] === undefined) && (
                    <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                    <table style={{width:"100%",minWidth:isMobile?760:undefined,borderCollapse:"collapse",fontSize:isMobile?12:13,background:"#fff"}}>
                      <thead>
                        <tr style={{background:"#f9fbf9"}}>
                          {["INTERVAL","MONTHS","RAIN INDEX","% OF VALUE","INSURED ACRES","LIABILITY","COV / ACRE","INDEMNITY","PREMIUM"].map(h=>(
                            <th key={h} style={{padding:isMobile?"8px 10px":"9px 14px",
                              textAlign:["INTERVAL","MONTHS"].includes(h)?"left":"right",
                              fontSize:10,fontWeight:800,letterSpacing:"0.09em",color:"#4a7a4a",
                              borderBottom:"2px solid #e4ece4",whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ivs.map((iv,i) => {
                          const code   = parsePRFCode(iv.intervalCode);
                          const season = PRF_INTERVAL_SEASON[code] || "spring";
                          const sc     = PRF_SEASON_COLOR[season];
                          const label  = PRF_INTERVAL_LABELS[code] || iv.intervalCode || "—";
                          const acres  = iv.insuredAcres != null ? iv.insuredAcres : null;
                          const ivCovPerAcre = acres > 0 ? Math.round(Number(iv.liability) / Number(acres)) : null;
                          // Per-row rainfall index. Prefer the RMA Final value (official,
                          // post-close). Fall back to NOAA estimated (live, in-progress) and
                          // tag it visually so it's clear which is which.
                          const gid = iv.gridId || iv.grid_id;
                          const rainEntry = gid ? rainfallIndex[`${gid}|${code || iv.intervalCode}`] : null;
                          // Open intervals read percent of normal TO DATE, never the index.
                          const rainRd    = rowReading(iv);
                          const rainVal   = rainRd.index != null ? rainRd.index : rainRd.toDate;
                          const rainTag   = rainRd.source === "FINAL" ? "FINAL"
                                          : rainRd.source === "ESTIMATED" ? "EST"
                                          : rainRd.source === "TO_DATE" ? "SO FAR" : null;
                          let rainColor = "#aaa";
                          if (rainVal != null) {
                            if (rainVal >= 100)      rainColor = "#1a6a3a";
                            else if (rainVal >= 90 || rainRd.index == null) rainColor = "#8a6d2b";
                            else                     rainColor = "#a02828";
                          }
                          return (
                            <tr key={i} style={{borderBottom:"1px solid #f0f4f0",background:i%2?"#fafcfa":"#fff"}}>
                              <td style={{padding:"10px 14px"}}>
                                <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:5,
                                  background:sc.bg,color:sc.color,border:`1px solid ${sc.color}33`}}>
                                  {code || iv.intervalCode}
                                </span>
                              </td>
                              <td style={{padding:"10px 14px",fontWeight:600,color:"#1a2e1a"}}>{label}</td>
                              <td title={
                                rainRd.source === "FINAL"
                                  ? `RMA Final Grid Index ${rainVal.toFixed(1)}% of normal`
                                  : rainRd.source === "ESTIMATED"
                                    ? `Closed. NOAA index ${rainVal.toFixed(1)}% of normal · RMA not yet published`
                                    : rainRd.source === "TO_DATE"
                                      ? `Rain so far is ${rainVal.toFixed(0)}% of normal for the same days${rainEntry?.estimatedAsOf ? `, through ${rainEntry.estimatedAsOf}` : ""}. No payment is figured until the interval closes.`
                                      : (rainRd.status === "upcoming" ? "Interval not started" : "No rainfall reading yet")
                              }
                                  style={{padding:isMobile?"8px 10px":"10px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:rainVal!=null?800:400,color:rainColor,whiteSpace:"nowrap"}}>
                                {rainVal != null ? rainVal.toFixed(0) + "%" : "—"}
                                {rainTag && (
                                  <span style={{marginLeft:5,fontSize:9,fontWeight:800,letterSpacing:"0.05em",padding:"1px 4px",border:`1px solid ${rainColor}55`,borderRadius:3,color:rainColor,opacity:0.85}}>
                                    {rainTag}
                                  </span>
                                )}
                              </td>
                              <td style={{padding:"10px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",color:"#555"}}>
                                {iv.percentValue!=null?(iv.percentValue*100).toFixed(0)+"%":"—"}
                              </td>
                              <td style={{padding:"10px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums"}}>
                                {acres != null ? Number(acres).toLocaleString(undefined,{maximumFractionDigits:2}) : "—"}
                              </td>
                              <td style={{padding:"10px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:700,color:"#1a6a3a"}}>
                                {"$"+(Number(iv.liability)||0).toLocaleString()}
                              </td>
                              <td style={{padding:"10px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",color:"#60735f",fontSize:12}}>
                                {ivCovPerAcre != null ? "$"+ivCovPerAcre.toLocaleString() : "—"}
                              </td>
                              {(() => {
                                const ind = rowIndemnity(iv);
                                // Dollars only once the interval has closed.
                                const showInd = ind.primary != null;
                                const ivIndColor = showInd && ind.primary > 0
                                  ? (ind.source === "FINAL" ? "#a02828" : "#8a6d2b")
                                  : "#aaa";
                                let tooltip;
                                if (ind.status === "open" || (ind.status === "closed" && ind.source === "TO_DATE")) {
                                  tooltip = "No payment is figured until the interval closes and its rain is all in.";
                                } else if (ind.primary != null) {
                                  tooltip = `${ind.label}. Liability × max(0, (coverage − grid index) / coverage).`;
                                } else {
                                  tooltip = "No grid index yet";
                                }
                                return (
                                  <td title={tooltip}
                                      style={{padding:"10px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:showInd && ind.primary>0?800:400,color:ivIndColor,whiteSpace:"nowrap"}}>
                                    {!showInd ? "—" : ind.primary === 0 ? "$0" : "$" + Math.round(ind.primary).toLocaleString()}
                                    {showInd && ind.primary > 0 && ind.source === "ESTIMATED" && (
                                      <span style={{marginLeft:5,fontSize:9,fontWeight:800,letterSpacing:"0.05em",padding:"1px 4px",border:`1px solid ${ivIndColor}55`,borderRadius:3,color:ivIndColor,opacity:0.85}}>
                                        EST
                                      </span>
                                    )}
                                  </td>
                                );
                              })()}
                              <td style={{padding:"10px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:800,color:"#1a2e1a"}}>
                                {"$"+(Number(iv.premium)||0).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{background:"#f4f8f4",borderTop:"2px solid #e4ece4"}}>
                          <td colSpan={4} style={{padding:"9px 14px",fontWeight:800,fontSize:11,color:"#4a7a4a",letterSpacing:"0.08em"}}>
                            GRID {displayGridId} TOTAL
                          </td>
                          <td style={{padding:"9px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:700,color:"#555"}}>
                            {gridAcres > 0 ? gridAcres.toLocaleString(undefined,{maximumFractionDigits:2}) : "—"}
                          </td>
                          <td style={{padding:"9px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:800,color:"#1a6a3a"}}>
                            {"$"+gridLiab.toLocaleString()}
                          </td>
                          <td style={{padding:"9px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",color:"#60735f",fontWeight:700,fontSize:12}}>
                            {covPerAcre > 0 ? "$"+covPerAcre.toLocaleString() : "—"}
                          </td>
                          {(() => {
                            // Grid total = closed intervals only, like the row cells.
                            let any = false, sum = 0;
                            for (const iv of ivs) {
                              const ind = rowIndemnity(iv);
                              if (ind.primary != null) { any = true; sum += ind.primary; }
                            }
                            const totalColor = any && sum > 0 ? "#a02828" : "#60735f";
                            return (
                              <td style={{padding:"9px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:any && sum > 0 ? 800 : 600,color:totalColor}}>
                                {any ? (sum > 0 ? "$"+Math.round(sum).toLocaleString() : "$0") : "—"}
                              </td>
                            );
                          })()}
                          <td style={{padding:"9px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:800}}>
                            {"$"+gridPrem.toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    </div>
                  )}
                </div>
              );
            })}

            {/* ── POLICY TOTAL ROW ── */}
            {grids.length > 1 && (
              <div style={{background:"#fff",border:"1px solid #e4ece4",borderRadius:8,padding:"12px 16px",
                display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
                <span style={{fontSize:11,fontWeight:800,letterSpacing:"0.1em",color:"#4a7a4a",textTransform:"uppercase"}}>
                  Policy Total · {allIntervals.length} intervals across {distinctGrids} grids
                </span>
                <div style={{display:"flex",gap:24,flexWrap:"wrap"}}>
                  <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,color:"#1a6a3a"}}>
                    ${totLiab.toLocaleString()} <span style={{fontWeight:400,color:"#60735f",fontSize:11}}>liability</span>
                  </span>
                  <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,color:"#1a2e1a"}}>
                    ${grossPrem.toLocaleString()} <span style={{fontWeight:400,color:"#60735f",fontSize:11}}>gross prem</span>
                  </span>
                  {netPrem != null && (
                    <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,color:"#2d5a2d"}}>
                      ${Number(netPrem).toLocaleString()} <span style={{fontWeight:400,color:"#60735f",fontSize:11}}>net prem</span>
                    </span>
                  )}
                  {estTotalIndemnity != null && (
                    <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,color: estTotalIndemnity > 0 ? "#a02828" : "#60735f"}}>
                      ${Math.round(estTotalIndemnity).toLocaleString()} <span style={{fontWeight:400,color:"#60735f",fontSize:11}}>{indemnityTotal && !indemnityTotal.allFinal ? "indemnity, est at close" : "indemnity"}</span>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── RAINFALL GRID MAP ──
              The same statewide map as Markets → PRF Grid, but handed ONLY this
              farm. That single prop is what makes it open on the right state:
              prfBookStateFips() reads the state off this farm's PRF policies, so
              an Ohio farm opens on Ohio instead of on the book's home state. Given
              the whole book — which is what the Markets and PRF pages pass — New
              York's ~29 PRF policies outvote a single out-of-state farm every time,
              and an agent looking at that farm lands on the wrong map.

              Their own grids keep the gold ring and the table scopes to them, so
              this reads as "where this farm sits" rather than as a state atlas. */}
          <div style={{ marginTop:24 }}>
            <SectionTitle>🗺️ Rainfall Grid Map</SectionTitle>
            {!showGridMap ? (
              <button onClick={() => setShowGridMap(true)}
                style={{ padding:"9px 16px", fontSize:13, fontWeight:700, color:"#2d4a2d",
                         background:"#e3f0e0", border:"1px solid #b9d2b5", borderRadius:8,
                         cursor:"pointer", fontFamily:"'Lora','Georgia',serif" }}>
                Open the grid map for this farm
              </button>
            ) : (
              <PRFGridMapView farmers={[farmer]} isMobile={isMobile}
                mode={isCurrentUserAdmin() ? "admin" : "portal"} />
            )}
          </div>

          {/* ── PRF CLAIMS HISTORY ── */}
          {/* PRF indemnities are rainfall-index-driven, not weather-cause.
              Cause-of-loss column is intentionally suppressed for non-MPCI
              products by FarmerClaimsHistory. */}
          <div style={{ marginTop:24 }}>
            <SectionTitle>📋 PRF Claims History</SectionTitle>
            <FarmerClaimsHistory farmer={farmer} productFilter="prf" />
          </div>
    </div>
  );
}


// LRP VIEW
// ─────────────────────────────────────────────────────────────────────────────
function LRPView({ farmer, isMobile }) {
  // Admin-only: every LRP indemnity figure on this card (see canSeeLrpIndemnity).
  const showLrpIndem = canSeeLrpIndemnity();
  const today = new Date(); today.setHours(0,0,0,0);
  const parseEndDate = (d) => {
    // Parse end date as local midnight to avoid UTC timezone shift
    if (!d) return new Date(NaN);
    const str = String(d);
    // Handle YYYY-MM-DD format — parse as local, not UTC
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(+m[1], +m[2]-1, +m[3]);
    return new Date(str);
  };
  const [showCattlePanel, setShowCattlePanel] = React.useState(false);
  const [cattleFutures, setCattleFutures] = React.useState(() => {
    try { const s = sessionStorage.getItem("cloverag_cattle_futures"); return s ? JSON.parse(s) : null; } catch { return null; }
  }); // { "Fed Cattle": {"2026-03": 195.50, ...}, "Feeder Cattle": {...}, updated: "2026-03-11" }
  // 'detail' = one row per endorsement (default). 'monthly' = rollup by end-date
  // month (head, coverage value, premium, indemnity summed). Dairy farmers
  // typically want monthly to see "how many calves are protected in July".
  // Persisted to sessionStorage so the PDF export (which lives outside this
  // component) can pick up the current selection.
  const [endorsementView, setEndorsementView] = React.useState(() => {
    try { return sessionStorage.getItem('cloverag_lrp_endorsement_view') || 'detail'; } catch { return 'detail'; }
  });
  React.useEffect(() => {
    try { sessionStorage.setItem('cloverag_lrp_endorsement_view', endorsementView); } catch {}
  }, [endorsementView]);

  // Flatten all endorsements across all policies, attach commodity from policy
  const allEnds = (farmer.lrpPolicies || []).flatMap(pol =>
    (pol.endorsements || []).map(e => ({ ...e, commodity: e.commodity || pol.commodity || 'Cattle' }))
  );

  if (!allEnds.length) {
    return (
      <div>
        <div style={{ background:"#1a2e1a", borderRadius:10, padding:"24px 28px", marginBottom:16 }}>
          <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:"#4a7a4a", textTransform:"uppercase", marginBottom:6 }}>Livestock Risk Protection</div>
          <h2 style={{ margin:"0 0 4px", fontSize:26, fontWeight:800, color:"#fff", fontFamily:"'Lora',serif" }}>{farmer.name}</h2>
        </div>
        <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #c8d8c8" }}>
          <div style={{ fontSize:32, marginBottom:10 }}>🐄</div>
          <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:6 }}>No LRP endorsements on file</div>
          <div style={{ color:"#60735f", fontSize:13 }}>Import the Rain & Hail LRP Excel export from the Import screen to populate this farmer's endorsements.</div>
        </Card>
      </div>
    );
  }

  // Split active vs ended
  const active = allEnds
    .filter(e => { const d = parseEndDate(e.endDate); return !isNaN(d) && d >= today; })
    .sort((a,b) => parseEndDate(a.endDate) - parseEndDate(b.endDate));
  const ended = allEnds
    .filter(e => { const d = parseEndDate(e.endDate); return isNaN(d) || d < today; })
    .sort((a,b) => parseEndDate(b.endDate) - parseEndDate(a.endDate)); // most recent first

  // Summary stats
  const activeHead     = active.reduce((s,e) => s + (e.head||0), 0);
  const totalPremium   = allEnds.reduce((s,e) => s + (e.producerPremium||0), 0);
  const totalGrossPrem = allEnds.reduce((s,e) => s + (e.premium||0), 0);
  const totalIndemnity = allEnds.reduce((s,e) => {
    // Prefer the explicit payableIndemnity from the Excel if present
    if (e.payableIndemnity != null && e.payableIndemnity > 0) return s + e.payableIndemnity;
    // Fall back to calculated indemnity from actualEndValue
    if (e.actualEndValue > 0 && e.actualEndValue < e.coveragePrice)
      return s + (e.coveragePrice - e.actualEndValue) * (e.head||0) * (e.targetWeight||1);
    return s;
  }, 0);

  // Commodity types present
  const commodities = [...new Set(allEnds.map(e => e.commodity).filter(Boolean))];

  // Total insured value on active endorsements (revenue guarantee equivalent)
  const totalInsuredValue = active.reduce((s,e) => s + ((e.coveragePrice||0) * (e.head||0) * (e.targetWeight||1)), 0);

  // Coverage price range on active (kept for tooltip/detail, but not shown as primary KPI)
  const activePrices = active.map(e => e.coveragePrice).filter(Boolean);
  const priceRange = activePrices.length
    ? (activePrices.length === 1
        ? fmtD(activePrices[0])
        : fmtD(Math.min(...activePrices)) + ' – ' + fmtD(Math.max(...activePrices)))
    : null;

  const EndTable = ({ rows, isActive, cattleFuturesData }) => {
    const [sortCol, setSortCol] = React.useState('endDate');
    const [sortDir, setSortDir] = React.useState(isActive ? 'asc' : 'desc');

    function toggleSort(col) {
      if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
      else { setSortCol(col); setSortDir('asc'); }
    }

    function getSortVal(e, col) {
      switch(col) {
        case 'commodity':      return (e.commodity||'').toLowerCase();
        case 'salesDate':      return new Date(e.salesDate||0).getTime();
        case 'endDate':        return new Date(e.endDate||0).getTime();
        case 'sellWindow':     return new Date(e.endDate||0).getTime(); // window opens 60d before End Date → order by End Date
        case 'weeksOut':       return e.weeksOut||0;
        case 'head':           return e.head||0;
        case 'targetWeight':   return e.targetWeight||0;
        case 'coveragePrice':  return e.coveragePrice||0;
        case 'actualEndValue': return e.actualEndValue||0;
        case 'indemnity':      return e.payableIndemnity||(e.coveragePrice>e.actualEndValue?(e.coveragePrice-e.actualEndValue)*(e.head||0)*(e.targetWeight||1):0);
        case 'producerPremium':return e.producerPremium||0;
        case 'premium':       return e.premium||0;
        case 'estEndValue':   return 0;
        case 'estIndemnity':  return 0;
        default: return 0;
      }
    }

    const sorted = [...rows].sort((a, b) => {
      const av = getSortVal(a, sortCol), bv = getSortVal(b, sortCol);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    // UBC = target weight under 1 cwt (unborn calf); same convention used below for
    // the /head vs /cwt coverage-price display.
    const showSellWindow = rows.some(e => (e.targetWeight||0) >= 1.0);

    const cols = [
      {h:"Commodity",        key:"commodity",       left:true},
      {h:"Sales Date",       key:"salesDate",       left:false},
      {h:"End Date",         key:"endDate",         left:false},
      // Marketing-window flag — only meaningful for active (un-ended) endorsements,
      // and only for real feeder/fed weights. UBC (target weight < 1 cwt) calves are
      // sold within days of birth, so the 60-day ownership rule never binds there.
      ...(isActive && showSellWindow ? [{h:"Sell Window", key:"sellWindow", left:false}] : []),
      {h:"Weeks",            key:"weeksOut",        left:false},
      {h:"Head",             key:"head",            left:false},
      {h:"Wt (cwt)",         key:"targetWeight",    left:false},
      {h:"Coverage Price",   key:"coveragePrice",   left:false},
      ...(isActive ? [
        ...(cattleFuturesData && showLrpIndem ? [{h:"CME Est. Value", key:"estEndValue", left:false}] : []),
        ...(cattleFuturesData && showLrpIndem ? [{h:"Est. Indemnity", key:"estIndemnity", left:false}] : []),
      ] : [
        {h:"Actual End Value", key:"actualEndValue", left:false},
        ...(showLrpIndem ? [{h:"Indemnity", key:"indemnity", left:false}] : []),
      ]),
      {h:"Producer Prem",    key:"producerPremium", left:false},
      {h:"Gross Prem",       key:"premium",         left:false},
    ];

    const arrow = (key) => sortCol === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    return (
    <div style={{ overflowX:"auto" }}>
      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
        <thead>
          <tr style={{ background:"#f4f8f4" }}>
            {cols.map(col => (
              <th key={col.key}
                onClick={() => toggleSort(col.key)}
                style={{ padding:"8px 12px", textAlign:col.left?"left":"right",
                  fontSize:10, fontWeight:800, letterSpacing:"0.08em",
                  color: sortCol===col.key ? "#1a2e1a" : "#60735f",
                  textTransform:"uppercase", borderBottom:"1px solid #e4ece4",
                  whiteSpace:"nowrap", cursor:"pointer", userSelect:"none",
                  background: sortCol===col.key ? "#eaf2ea" : "transparent",
                  transition:"background 0.1s" }}>
                {col.h}{arrow(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((e, i) => {
            const endDt    = parseEndDate(e.endDate);
            const daysLeft = isActive ? Math.ceil((endDt - today) / (1000*60*60*24)) : null;
            const soonFlag = daysLeft != null && daysLeft <= 45;
            const settled  = !isActive && e.actualEndValue > 0;
            // paidOut: prefer explicit payableIndemnity from RMA, fall back to per-unit calc
            // Don't use AEV < coveragePrice alone — section agreements have mixed units
            // where some units settle below coverage but net indemnity is still zero
            const paidOut  = settled && (
              e.payableIndemnity > 0 ||
              (!e.payableIndemnity && e.actualEndValue < e.coveragePrice)
            );
            const indemnity = e.payableIndemnity > 0
              ? e.payableIndemnity
              : paidOut
                ? (e.coveragePrice - e.actualEndValue) * (e.head||0) * (e.targetWeight||1)
                : 0;
            // Commodity color
            const isFed    = (e.commodity||'').toLowerCase().includes('fed') && !(e.commodity||'').toLowerCase().includes('feeder');
            const comColor = isFed ? {bg:"#fdf1e8", color:"#7a3b10", border:"#c8860a"} : {bg:"#eef6ec", color:"#2d5a10", border:"#4a7a2a"};
            return (
              <tr key={i} style={{ borderBottom:"1px solid #f0f4f0", background: soonFlag ? "#fffbf0" : i%2===0 ? "#fff" : "#fafcfa" }}>
                <td style={{ padding:"10px 12px" }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:4, background:comColor.bg, color:comColor.color, border:`1px solid ${comColor.border}44` }}>
                    {e.commodity || "Cattle"}
                  </span>
                </td>
                <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", color:"#666", fontSize:12 }}>{e.salesDate || "—"}</td>
                <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                  <div style={{ fontWeight:700 }}>{e.endDate}</div>
                  {soonFlag && <div style={{ fontSize:10, color:"#c8860a", fontWeight:700 }}>⚡ {daysLeft}d</div>}
                </td>
                {isActive && showSellWindow && (() => {
                  // UBC calves are marketed within days of birth — the ownership
                  // window is never the binding constraint, so no badge.
                  if ((e.targetWeight||0) < 1.0) {
                    return <td style={{ padding:"10px 12px", textAlign:"center", color:"#c8d8c8" }}>—</td>;
                  }
                  // LRP rule: must own until the last 60 days before the End Date.
                  // So you can market these cattle once you're within 60 days of
                  // the End Date; selling earlier voids the coverage. Indemnity
                  // still settles at the End Date off the CME index regardless.
                  const inWindow = daysLeft != null && daysLeft <= 60;
                  const opensIn  = daysLeft != null ? daysLeft - 60 : null;
                  const openDate = parseEndDate(e.endDate); openDate.setDate(openDate.getDate() - 60);
                  const openStr  = isNaN(openDate) ? "" : openDate.toLocaleDateString("en-US", { month:"short", day:"numeric" });
                  return (
                    <td style={{ padding:"10px 12px", textAlign:"center", whiteSpace:"nowrap" }}>
                      {inWindow ? (
                        <span title={`Within 60 days of the End Date — you can market these cattle and keep coverage (indemnity settles at the End Date).`}
                          style={{ fontSize:11, fontWeight:700, color:"#1f6f3a", background:"#e6f4e8", border:"1px solid #1f6f3a33", borderRadius:10, padding:"3px 9px" }}>
                          ✓ In window
                        </span>
                      ) : (
                        <span title={`Selling now would void coverage — you must own these until the last 60 days before the End Date. Window opens ${openStr} (${opensIn}d).`}
                          style={{ fontSize:11, fontWeight:700, color:"#9a1f1f", background:"#fde0e0", border:"1px solid #9a1f1f33", borderRadius:10, padding:"3px 9px" }}>
                          ✕ Too early <span style={{ fontWeight:600, opacity:0.85 }}>· {opensIn}d</span>
                        </span>
                      )}
                    </td>
                  );
                })()}
                <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", color:"#666" }}>{e.weeksOut}w</td>
                <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700 }}>{e.head}</td>
                <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>{e.targetWeight}</td>
                <td style={{ padding:"10px 12px", textAlign:"right" }}>
                  { (() => {
                    const isUBC = (e.targetWeight||0) < 1.0;
                    const displayVal = isUBC ? (e.coveragePrice * (e.targetWeight||1)) : e.coveragePrice;
                    const label = isUBC ? '/head' : '/cwt';
                    return (
                      <div style={{ display:"inline-block", background:"#f0f7ff", border:"1px solid #b8d0f044", borderRadius:5, padding:"3px 10px" }}>
                        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#1a4a7a", fontSize:14 }}>{fmtD(displayVal)}</span>
                        <span style={{ fontSize:10, color:"#7a9aaa", marginLeft:3 }}>{label}</span>
                      </div>
                    );
                  })() }
                </td>
                {isActive && cattleFuturesData && showLrpIndem && (() => {
                  // Look up CME futures estimate for this endorsement's end month
                  const endD = parseEndDate(e.endDate);
                  const endMonthKey = endD.getFullYear()+"-"+(endD.getMonth()+1<10?"0":"")+(endD.getMonth()+1);
                  const commodity = (e.commodity||"").toLowerCase();
                  const isFed = commodity.includes("fed") && !commodity.includes("feeder");
                  const cattleType = isFed ? "Fed Cattle" : "Feeder Cattle";
                  const cmePrice = cattleFuturesData?.[cattleType]?.[endMonthKey] ?? null;
                  // For LRP, coverage/actual are $/cwt. Indemnity = max(0, coveragePrice - actual) * head * targetWeight
                  const estIndemnity = cmePrice != null && cmePrice < e.coveragePrice
                    ? (e.coveragePrice - cmePrice) * (e.head||0) * (e.targetWeight||1)
                    : cmePrice != null ? 0 : null;
                  return (
                    <>
                      <td style={{ padding:"10px 12px", textAlign:"right" }}>
                        {cmePrice != null ? (
                          <div style={{ display:"inline-block", background:"#fef7ee", border:"1px solid #e8b85044", borderRadius:5, padding:"3px 10px" }}>
                            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#b85000", fontSize:14 }}>{fmtD(cmePrice)}</span>
                            <span style={{ fontSize:9, color:"#b85000", marginLeft:3 }}>CME</span>
                          </div>
                        ) : <span style={{ color:"#bbb", fontSize:11 }}>—</span>}
                      </td>
                      <td style={{ padding:"10px 12px", textAlign:"right" }}>
                        {estIndemnity != null ? (
                          estIndemnity > 0 ? (
                            <span style={{ fontSize:12, fontWeight:800, color:"#b85000" }}>⚡ {fmtD0(estIndemnity)}</span>
                          ) : (
                            <span style={{ fontSize:11, fontWeight:700, color:"#2d5a2d" }}>No trigger</span>
                          )
                        ) : <span style={{ color:"#bbb", fontSize:11 }}>—</span>}
                      </td>
                    </>
                  );
                })()}
                {!isActive && <>
                  <td style={{ padding:"10px 12px", textAlign:"right" }}>
                    {settled ? (
                      <div style={{ display:"inline-block", background:(showLrpIndem&&paidOut)?"#fdecea":"#eef6ec", border:`1px solid ${(showLrpIndem&&paidOut)?"#e07070":"#4a7a2a"}44`, borderRadius:5, padding:"3px 10px" }}>
                        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:(showLrpIndem&&paidOut)?"#c0392b":"#2d7a2d", fontSize:14 }}>{fmtD(e.actualEndValue)}</span>
                      </div>
                    ) : <span style={{ color:"#bbb", fontSize:12 }}>—</span>}
                  </td>
                  {showLrpIndem && <td style={{ padding:"10px 12px" }}>
                    {e.payableIndemnity > 0
                      ? <span style={{ fontSize:11, fontWeight:800, color:"#2d7a2d" }}>✓ {fmtD0(e.payableIndemnity)}</span>
                      : settled
                        ? paidOut
                          ? <span style={{ fontSize:11, fontWeight:800, color:"#2d7a2d" }}>✓ {fmtD0(indemnity)}</span>
                          : <span style={{ fontSize:11, fontWeight:700, color:"#aaa" }}>No indemnity</span>
                        : <span style={{ fontSize:11, color:"#bbb" }}>Pending</span>}
                  </td>}
                </>}
                <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#2d4a2d" }}>
                  {e.producerPremium > 0 ? fmtD0(e.producerPremium) : <span style={{ color:"#bbb" }}>—</span>}
                </td>
                <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:600, color:"#666" }}>
                  {e.premium > 0 ? fmtD0(e.premium) : <span style={{ color:"#bbb" }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    );
  };

  // Roll up endorsements by end-date month for the monthly-summary view.
  // Dairy farmers want "how many calves are protected ending in July" without
  // scanning a row-per-endorsement table. Returns sorted-by-month array.
  function groupByMonth(endorsements, isActive) {
    const groups = {};
    for (const e of endorsements) {
      const d = parseEndDate(e.endDate);
      if (isNaN(d)) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      if (!groups[key]) groups[key] = {
        key,
        label: d.toLocaleDateString('en-US', { month:'long', year:'numeric' }),
        endorsements: [],
        head: 0,
        coverageValue: 0,
        producerPremium: 0,
        grossPremium: 0,
        indemnity: 0,
        commodities: new Set(),
      };
      const g = groups[key];
      g.endorsements.push(e);
      g.head += e.head || 0;
      g.coverageValue += (e.coveragePrice || 0) * (e.head || 0) * (e.targetWeight || 1);
      g.producerPremium += e.producerPremium || 0;
      g.grossPremium += e.premium || 0;
      if (e.payableIndemnity > 0) g.indemnity += e.payableIndemnity;
      else if (e.actualEndValue > 0 && e.actualEndValue < e.coveragePrice) {
        g.indemnity += (e.coveragePrice - e.actualEndValue) * (e.head || 0) * (e.targetWeight || 1);
      }
      if (e.commodity) g.commodities.add(e.commodity);
    }
    const arr = Object.values(groups);
    return arr.sort((a, b) => isActive ? a.key.localeCompare(b.key) : b.key.localeCompare(a.key));
  }

  const MonthlySummaryTable = ({ rows, isActive }) => {
    const months = groupByMonth(rows, isActive);
    if (months.length === 0) return null;
    const today2 = new Date(); today2.setHours(0,0,0,0);
    const totalHead = months.reduce((s,m)=>s+m.head,0);
    const totalCov  = months.reduce((s,m)=>s+m.coverageValue,0);
    const totalProd = months.reduce((s,m)=>s+m.producerPremium,0);
    const totalGros = months.reduce((s,m)=>s+m.grossPremium,0);
    const totalInd  = months.reduce((s,m)=>s+m.indemnity,0);

    // Mobile: stacked cards, no horizontal scroll
    if (isMobile) {
      return (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {months.map(m => {
            const monthDate = new Date(`${m.key}-01T00:00:00`);
            const daysToMonth = Math.ceil((monthDate - today2) / (1000*60*60*24));
            const soonFlag = isActive && daysToMonth >= 0 && daysToMonth <= 45;
            const commodityList = [...m.commodities].join(' + ') || 'Cattle';
            return (
              <div key={m.key} style={{ background:"#fff", border:"1px solid #e4ece4", borderRadius:10, padding:"12px 14px", borderLeft:`4px solid ${soonFlag ? '#c8860a' : '#2d4a2d'}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4, gap:8 }}>
                  <span style={{ fontWeight:800, fontSize:15, color:"#1a2e1a" }}>{m.label}</span>
                  <span style={{ whiteSpace:"nowrap" }}>
                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:18, color:"#1a2e1a" }}>{m.head.toLocaleString()}</span>
                    <span style={{ fontSize:11, color:"#60735f", marginLeft:4 }}>head</span>
                  </span>
                </div>
                <div style={{ fontSize:11, color:"#5a7a5a", marginBottom:10 }}>
                  {commodityList} · {m.endorsements.length} endorsement{m.endorsements.length !== 1 ? 's' : ''}
                  {soonFlag && <span style={{ marginLeft:6, color:"#c8860a", fontWeight:700 }}>⚡ within 45d</span>}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 12px", fontSize:12 }}>
                  <div>
                    <div style={{ color:"#60735f", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>Coverage Value</div>
                    <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#1a4a7a", fontSize:13 }}>{fmtD0(m.coverageValue)}</div>
                  </div>
                  <div>
                    <div style={{ color:"#60735f", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>Producer Prem</div>
                    <div style={{ fontVariantNumeric: "tabular-nums", fontSize:13 }}>{fmtD0(m.producerPremium)}</div>
                  </div>
                  <div>
                    <div style={{ color:"#60735f", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>Gross Prem</div>
                    <div style={{ fontVariantNumeric: "tabular-nums", fontSize:13, color:"#60735f" }}>{fmtD0(m.grossPremium)}</div>
                  </div>
                  {!isActive && showLrpIndem && (
                    <div>
                      <div style={{ color:"#60735f", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>Indemnity</div>
                      <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, fontSize:13, color: m.indemnity > 0 ? "#2d7a2d" : "#bbb" }}>{m.indemnity > 0 ? fmtD0(m.indemnity) : "—"}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {/* Total card */}
          <div style={{ background:"#1a2e1a", color:"#fff", borderRadius:10, padding:"12px 14px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8, gap:8 }}>
              <span style={{ fontWeight:800, fontSize:13, letterSpacing:"0.06em", textTransform:"uppercase" }}>Total</span>
              <span style={{ whiteSpace:"nowrap" }}>
                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:18 }}>{totalHead.toLocaleString()}</span>
                <span style={{ fontSize:11, color:"#a8c8a8", marginLeft:4 }}>head</span>
              </span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 12px", fontSize:12 }}>
              <div>
                <div style={{ color:"#a8c8a8", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>Coverage Value</div>
                <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, fontSize:13 }}>{fmtD0(totalCov)}</div>
              </div>
              <div>
                <div style={{ color:"#a8c8a8", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>Producer Prem</div>
                <div style={{ fontVariantNumeric: "tabular-nums", fontSize:13 }}>{fmtD0(totalProd)}</div>
              </div>
              <div>
                <div style={{ color:"#a8c8a8", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>Gross Prem</div>
                <div style={{ fontVariantNumeric: "tabular-nums", fontSize:13, color:"#a8c8a8" }}>{fmtD0(totalGros)}</div>
              </div>
              {!isActive && showLrpIndem && (
                <div>
                  <div style={{ color:"#a8c8a8", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>Indemnity</div>
                  <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, fontSize:13 }}>{fmtD0(totalInd)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#f4f8f4" }}>
              {["End Month","Commodity","Endorsements","Head","Coverage Value","Producer Prem","Gross Prem", (isActive || !showLrpIndem) ? null : "Indemnity"].filter(Boolean).map(h => (
                <th key={h} style={{ padding:"8px 12px", textAlign: h === "End Month" || h === "Commodity" ? "left" : "right", fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#60735f", textTransform:"uppercase", borderBottom:"1px solid #e4ece4", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {months.map((m, i) => {
              const today2 = new Date(); today2.setHours(0,0,0,0);
              const monthDate = new Date(`${m.key}-01T00:00:00`);
              const daysToMonth = Math.ceil((monthDate - today2) / (1000*60*60*24));
              const soonFlag = isActive && daysToMonth >= 0 && daysToMonth <= 45;
              const commodityList = [...m.commodities].join(' + ') || 'Cattle';
              return (
                <tr key={m.key} style={{ borderBottom:"1px solid #f0f4f0", background: soonFlag ? "#fffbf0" : i%2===0 ? "#fff" : "#fafcfa" }}>
                  <td style={{ padding:"10px 12px", fontWeight:800, color:"#1a2e1a" }}>
                    {m.label}
                    {soonFlag && <span style={{ marginLeft:8, fontSize:10, color:"#c8860a", fontWeight:700 }}>⚡ soon</span>}
                  </td>
                  <td style={{ padding:"10px 12px", fontSize:11, color:"#5a7a5a" }}>{commodityList}</td>
                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", color:"#666" }}>{m.endorsements.length}</td>
                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:14, color:"#1a2e1a" }}>{m.head.toLocaleString()}</td>
                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#1a4a7a" }}>{fmtD0(m.coverageValue)}</td>
                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>{fmtD0(m.producerPremium)}</td>
                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", color:"#60735f" }}>{fmtD0(m.grossPremium)}</td>
                  {!isActive && showLrpIndem && (
                    <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color: m.indemnity > 0 ? "#2d7a2d" : "#bbb" }}>
                      {m.indemnity > 0 ? fmtD0(m.indemnity) : "—"}
                    </td>
                  )}
                </tr>
              );
            })}
            {/* Totals row */}
            <tr style={{ borderTop:"2px solid #1a2e1a", background:"#1a2e1a", color:"#fff" }}>
              <td style={{ padding:"10px 12px", fontWeight:800, fontSize:11, letterSpacing:"0.06em", textTransform:"uppercase" }}>Total</td>
              <td style={{ padding:"10px 12px" }}></td>
              <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", color:"#a8c8a8" }}>{months.reduce((s,m)=>s+m.endorsements.length,0)}</td>
              <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:14 }}>{months.reduce((s,m)=>s+m.head,0).toLocaleString()}</td>
              <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700 }}>{fmtD0(months.reduce((s,m)=>s+m.coverageValue,0))}</td>
              <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>{fmtD0(months.reduce((s,m)=>s+m.producerPremium,0))}</td>
              <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", color:"#a8c8a8" }}>{fmtD0(months.reduce((s,m)=>s+m.grossPremium,0))}</td>
              {!isActive && showLrpIndem && (
                <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800 }}>{fmtD0(months.reduce((s,m)=>s+m.indemnity,0))}</td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Toggle pills shared between Active and Ended sections.
  const ViewToggle = () => (
    <div style={{ display:"flex", gap:4, marginLeft:"auto", background:"#f4f8f4", borderRadius:8, padding:3, border:"1px solid #e4ece4" }}>
      {[
        { key:'detail',  label:'Detail',  hint:'One row per endorsement' },
        { key:'monthly', label:'By Month', hint:'Roll up by end-date month' },
      ].map(opt => (
        <button key={opt.key}
          onClick={() => setEndorsementView(opt.key)}
          title={opt.hint}
          style={{ padding:"5px 12px", borderRadius:6, border:"none", cursor:"pointer",
            background: endorsementView === opt.key ? "#1a2e1a" : "transparent",
            color:     endorsementView === opt.key ? "#fff"    : "#5a7a5a",
            fontSize:11, fontWeight:700, letterSpacing:"0.04em" }}>
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <div>
      {/* Dark header */}
      <div style={{ background:"#1a2e1a", borderRadius:10, padding:"24px 28px", marginBottom:16 }}>
        <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:"#4a7a4a", textTransform:"uppercase", marginBottom:6 }}>
          Livestock Risk Protection
        </div>
        <h2 style={{ margin:"0 0 4px", fontSize:26, fontWeight:800, color:"#fff", fontFamily:"'Lora',serif" }}>
          {farmer.name}
        </h2>
        <div style={{ fontSize:12, color:"#7aaa7a", marginBottom:16, display:"flex", gap:8, flexWrap:"wrap" }}>
          {commodities.map(c => <span key={c} style={{ background:"#2d4a2d", padding:"1px 8px", borderRadius:3, fontWeight:700 }}>{c}</span>)}
          {farmer.policyNumber && <><span>·</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{farmer.policyNumber}</span></>}
        </div>
        {/* Stats row */}
        <div style={{ display:"flex", gap:40, flexWrap:"wrap" }}>
          {[
            { val: String(activeHead), lbl: "ACTIVE HEAD COVERED" },
            { val: fmtD0(totalPremium), lbl: "NET PREMIUM (PRODUCER)" },
            ...(totalGrossPrem > 0 ? [{ val: fmtD0(totalGrossPrem), lbl: "GROSS PREMIUM" }] : []),
            ...(totalInsuredValue > 0 ? [{ val: fmtD0(totalInsuredValue), lbl: "REVENUE GUARANTEE" }] : []),
            ...(showLrpIndem && totalIndemnity > 0 ? [{ val: fmtD0(totalIndemnity), lbl: "TOTAL INDEMNITY EARNED" }] : []),
          ].map(s => (
            <div key={s.lbl}>
              <div style={{ fontSize:24, fontWeight:800, color:"#fff", lineHeight:1 }}>{s.val}</div>
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#4a7a4a", marginTop:3 }}>{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Active endorsements */}
      {active.length > 0 && (
        <Card style={{ marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, flexWrap:"wrap" }}>
            <SectionTitle style={{ margin:0 }}>Active Endorsements</SectionTitle>
            <span style={{ fontSize:11, fontWeight:800, background:"#eef6ec", color:"#2d5a10", border:"1px solid #4a7a2a44", borderRadius:10, padding:"1px 9px" }}>{active.length}</span>
            <ViewToggle />
          </div>
          {endorsementView === 'monthly'
            ? <MonthlySummaryTable rows={active} isActive={true} />
            : <EndTable rows={active} isActive={true} cattleFuturesData={cattleFutures} />}
        </Card>
      )}

      {/* Ended endorsements */}
      {ended.length > 0 && (
        <Card style={{ marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, flexWrap:"wrap" }}>
            <SectionTitle style={{ margin:0 }}>Ended Endorsements</SectionTitle>
            <span style={{ fontSize:11, fontWeight:800, background:"#f4f4f4", color:"#888", border:"1px solid #ddd", borderRadius:10, padding:"1px 9px" }}>{ended.length}</span>
            {showLrpIndem && totalIndemnity > 0 && <span style={{ fontSize:11, fontWeight:800, color:"#2d7a2d" }}>✓ {fmtD0(totalIndemnity)} earned</span>}
            <ViewToggle />
          </div>
          {endorsementView === 'monthly'
            ? <MonthlySummaryTable rows={ended} isActive={false} />
            : <EndTable rows={ended} isActive={false} cattleFuturesData={cattleFutures} />}
        </Card>
      )}

      {/* ── CME CATTLE FUTURES ENTRY ── admin-only: it exists to estimate indemnities */}
      {active.length > 0 && showLrpIndem && (
        <div style={{marginTop:0,marginBottom:16,borderRadius:7,border:"1px solid #e8b85044",overflow:"hidden"}}>
          <button onClick={()=>setShowCattlePanel(p=>!p)} style={{width:"100%",display:"flex",alignItems:"center",
            justifyContent:"space-between",padding:"12px 16px",background:"#fef7ee",border:"none",cursor:"pointer"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:14}}>🐄</span>
              <strong style={{fontSize:12,color:"#7a4a00"}}>CME Cattle Futures</strong>
              <span style={{fontSize:11,color:"#b85000",fontWeight:500}}>
                — enter settlement prices for estimated indemnities on active endorsements
              </span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {cattleFutures?.updated && (
                <span style={{fontSize:10,color:"#7a6a00",fontWeight:600,background:"#fff4e6",
                  padding:"2px 8px",borderRadius:10}}>
                  Updated {cattleFutures.updated}
                </span>
              )}
              <span style={{color:"#b85000",fontSize:14,transition:"transform 0.2s",display:"inline-block",
                transform:showCattlePanel?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </div>
          </button>
          {showCattlePanel && (() => {
            const monthMap = {};
            active.forEach(e => {
              const endD = parseEndDate(e.endDate);
              if (isNaN(endD)) return;
              const k = endD.getFullYear()+"-"+(endD.getMonth()+1<10?"0":"")+(endD.getMonth()+1);
              const commodity = (e.commodity||"").toLowerCase();
              const isFed = commodity.includes("fed") && !commodity.includes("feeder");
              const type = isFed ? "Fed Cattle" : "Feeder Cattle";
              if (!monthMap[k]) monthMap[k] = new Set();
              monthMap[k].add(type);
            });
            const sortedMonths = Object.keys(monthMap).sort();
            const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const hasFed = active.some(e => {const c=(e.commodity||"").toLowerCase(); return c.includes("fed") && !c.includes("feeder");});
            const hasFeeder = active.some(e => {const c=(e.commodity||"").toLowerCase(); return c.includes("feeder") || (!c.includes("fed"));});

            const handleCattleSave = () => {
              const newFed = {};
              const newFeeder = {};
              sortedMonths.forEach(k => {
                const fedEl = document.getElementById("cattle-fed-"+k);
                const feederEl = document.getElementById("cattle-feeder-"+k);
                const fedVal = parseFloat(fedEl?.value);
                const feederVal = parseFloat(feederEl?.value);
                if (!isNaN(fedVal) && fedVal > 0) newFed[k] = Math.round(fedVal*100)/100;
                if (!isNaN(feederVal) && feederVal > 0) newFeeder[k] = Math.round(feederVal*100)/100;
              });
              const updated = new Date().toISOString().slice(0,10);
              const newData = { "Fed Cattle": newFed, "Feeder Cattle": newFeeder, updated };
              setCattleFutures(newData);
              try { sessionStorage.setItem("cloverag_cattle_futures", JSON.stringify(newData)); } catch {}
            };

            const handleCattleClear = () => {
              setCattleFutures(null);
              try { sessionStorage.removeItem("cloverag_cattle_futures"); } catch {}
            };

            return (
              <div style={{background:"#fffbf5",padding:"14px 16px",borderTop:"1px solid #e8b85033"}}>
                <div style={{fontSize:11,color:"#7a4a00",marginBottom:12,lineHeight:1.5}}>
                  Enter CME daily settlement prices ($/cwt) from{" "}
                  <a href="https://www.cmegroup.com/markets/agriculture/livestock/live-cattle.settlements.html"
                    target="_blank" rel="noopener noreferrer" style={{color:"#7a3b10",fontWeight:700}}>
                    Live Cattle
                  </a>{" / "}
                  <a href="https://www.cmegroup.com/markets/agriculture/livestock/feeder-cattle.settlements.html"
                    target="_blank" rel="noopener noreferrer" style={{color:"#2d5a10",fontWeight:700}}>
                    Feeder Cattle
                  </a>{" "}
                  settlement pages. Only months with active endorsements are shown.
                </div>
                <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:380}}>
                    <thead>
                      <tr style={{borderBottom:"2px solid #e8d0a8"}}>
                        <th style={{textAlign:"left",padding:"6px 10px",fontSize:10,fontWeight:800,
                          color:"#7a5a2a",textTransform:"uppercase",letterSpacing:"0.08em"}}>Month</th>
                        <th style={{textAlign:"center",padding:"6px 10px",fontSize:10,fontWeight:800,
                          color:"#7a5a2a",textTransform:"uppercase",letterSpacing:"0.08em"}}>Endorsements</th>
                        {hasFed && <th style={{textAlign:"center",padding:"6px 10px",fontSize:10,fontWeight:800,
                          color:"#7a3b10",textTransform:"uppercase",letterSpacing:"0.08em"}}>Fed Cattle ($/cwt)</th>}
                        {hasFeeder && <th style={{textAlign:"center",padding:"6px 10px",fontSize:10,fontWeight:800,
                          color:"#2d5a10",textTransform:"uppercase",letterSpacing:"0.08em"}}>Feeder Cattle ($/cwt)</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMonths.map((k, i) => {
                        const [y, m] = k.split("-").map(Number);
                        const label = MONTH_NAMES[m-1] + " " + y;
                        const types = monthMap[k];
                        const endCount = active.filter(e => {
                          const d = parseEndDate(e.endDate);
                          return d.getFullYear()===y && d.getMonth()+1===m;
                        }).length;
                        return (
                          <tr key={k} style={{borderBottom: i < sortedMonths.length-1 ? "1px solid #ece0cc" : "none"}}>
                            <td style={{padding:"8px 10px",fontWeight:700,color:"#1a2e1a"}}>{label}</td>
                            <td style={{padding:"8px 10px",textAlign:"center",fontSize:11,color:"#7a6a00",fontWeight:600}}>
                              {endCount} end{endCount!==1?"s":""}
                            </td>
                            {hasFed && (
                              <td style={{padding:"4px 10px",textAlign:"center"}}>
                                <input id={"cattle-fed-"+k} type="number" step="0.01" min="0" max="9999"
                                  defaultValue={cattleFutures?.["Fed Cattle"]?.[k] || ""}
                                  placeholder={types.has("Fed Cattle") ? "—" : "n/a"}
                                  disabled={!types.has("Fed Cattle")}
                                  style={{width:90,padding:"5px 8px",borderRadius:5,
                                    border:"1px solid "+(types.has("Fed Cattle")?"#d8c0a0":"#eee"),
                                    fontSize:13,fontVariantNumeric: "tabular-nums",fontWeight:700,textAlign:"center",
                                    color:types.has("Fed Cattle")?"#7a3b10":"#ccc",
                                    background:types.has("Fed Cattle")?"#fff":"#f8f8f8"}}
                                />
                              </td>
                            )}
                            {hasFeeder && (
                              <td style={{padding:"4px 10px",textAlign:"center"}}>
                                <input id={"cattle-feeder-"+k} type="number" step="0.01" min="0" max="9999"
                                  defaultValue={cattleFutures?.["Feeder Cattle"]?.[k] || ""}
                                  placeholder={types.has("Feeder Cattle") ? "—" : "n/a"}
                                  disabled={!types.has("Feeder Cattle")}
                                  style={{width:90,padding:"5px 8px",borderRadius:5,
                                    border:"1px solid "+(types.has("Feeder Cattle")?"#b8d0b8":"#eee"),
                                    fontSize:13,fontVariantNumeric: "tabular-nums",fontWeight:700,textAlign:"center",
                                    color:types.has("Feeder Cattle")?"#2d5a10":"#ccc",
                                    background:types.has("Feeder Cattle")?"#fff":"#f8f8f8"}}
                                />
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{display:"flex",gap:10,marginTop:12,alignItems:"center",flexWrap:"wrap"}}>
                  <button onClick={handleCattleSave}
                    style={{padding:"8px 20px",borderRadius:7,border:"none",background:"#b85000",
                      color:"#fff",fontWeight:800,fontSize:12,cursor:"pointer"}}>
                    💾 Save & Apply Estimates
                  </button>
                  {cattleFutures && (
                    <button onClick={handleCattleClear}
                      style={{padding:"8px 16px",borderRadius:7,border:"1px solid #e0a0a0",background:"#fff",
                        color:"#c0392b",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                      Clear All
                    </button>
                  )}
                  <span style={{fontSize:10,color:"#999",marginLeft:8}}>
                    Saved to browser session · Estimates shown in orange on active endorsements
                  </span>
                </div>
                <div style={{marginTop:10,padding:"8px 12px",background:"#fffbe8",border:"1px solid #e8d870",
                  borderRadius:5,fontSize:10,color:"#7a6a00",lineHeight:1.5}}>
                  <strong>⚠ Disclaimer:</strong> CME futures prices are used to <em>estimate</em> potential indemnities only.
                  Actual end values are determined by USDA/RMA based on official price reporting at endorsement expiration.
                  These estimates are not official and should not be used for financial planning decisions.
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── LRP CLAIMS HISTORY ── */}
      {/* Filed/paid LRP indemnities sourced from M13 P20/P20A (claim header +
          disbursements) tagged policy_type='lrp'. Only LRP claims are
          surfaced here; non-LRP claims appear on their respective product
          pages. */}
      {showLrpIndem && (
        <div style={{ marginTop:24 }}>
          <SectionTitle>📋 LRP Claims History</SectionTitle>
          <FarmerClaimsHistory farmer={farmer} productFilter="lrp" />
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// DRP VIEW
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// DRP VIEW  (flat functions — no nested component definitions)
// ─────────────────────────────────────────────────────────────────────────────

function drpQColor(lbl) {
  const m = lbl?.match(/Q(\d)/); if (!m) return {bg:"#eeeeee",color:"#555"};
  const q = parseInt(m[1]);
  if (q===1) return {bg:"#e8f0fa",color:"#1a4a7a"};  // Q1 blue
  if (q===2) return {bg:"#eaf5e8",color:"#1a5a1a"};  // Q2 green
  if (q===3) return {bg:"#fdf4e8",color:"#7a4a10"};  // Q3 amber
  if (q===4) return {bg:"#f3eafa",color:"#5a1a7a"};  // Q4 purple
  return {bg:"#eeeeee",color:"#555"};
}
// ── USDA AMS ANNOUNCED CLASS III / IV PRICES ─────────────────────────────
// Source: USDA AMS Announcement of Class and Component Prices (slug 3355).
// Monthly prices per cwt. Quarterly actual = avg of 3 months.
//
// NOTE (Apr 2026): This object is now AUTO-HYDRATED at app startup from the
// `class_prices` Supabase table (fed by /api/ams-sync). The hardcoded values
// below remain as a fallback in case the DB is unreachable. You no longer
// need to manually update this object — just let the monthly ams-sync cron
// (Vercel: 7th of month @ 14:00 UTC) pick up new AMS releases automatically.
const USDA_MONTHLY_PRICES = {
  // Class III ($/cwt)
  III: {
    "2021-01":16.04,"2021-02":15.75,"2021-03":16.15,"2021-04":17.67,"2021-05":18.96,"2021-06":17.21,
    "2021-07":16.49,"2021-08":15.95,"2021-09":16.53,"2021-10":17.83,"2021-11":18.03,"2021-12":18.36,
    "2022-01":20.38,"2022-02":20.91,"2022-03":22.45,"2022-04":24.42,"2022-05":25.21,"2022-06":24.33,
    "2022-07":22.52,"2022-08":20.10,"2022-09":19.82,"2022-10":21.81,"2022-11":21.01,"2022-12":20.50,
    "2023-01":19.43,"2023-02":17.78,"2023-03":18.10,"2023-04":18.52,"2023-05":16.11,"2023-06":14.91,
    "2023-07":13.77,"2023-08":17.19,"2023-09":18.39,"2023-10":16.84,"2023-11":17.15,"2023-12":16.04,
    "2024-01":15.17,"2024-02":16.08,"2024-03":16.34,"2024-04":15.50,"2024-05":18.55,"2024-06":19.87,
    "2024-07":19.79,"2024-08":20.66,"2024-09":23.34,"2024-10":22.85,"2024-11":19.95,"2024-12":18.62,
    "2025-01":20.34,"2025-02":20.18,"2025-03":18.62,"2025-04":17.48,"2025-05":18.57,"2025-06":18.82,
    "2025-07":17.32,"2025-08":17.24,"2025-09":17.59,"2025-10":16.91,"2025-11":17.18,"2025-12":15.86,
    "2026-01":14.59,
    "2026-02":14.94, // USDA AMS announced March 4, 2026
    // 2026-03: NOT YET ANNOUNCED
  },
  // Class IV ($/cwt)
  IV: {
    "2021-01":13.75,"2021-02":13.19,"2021-03":14.18,"2021-04":15.42,"2021-05":16.16,"2021-06":16.35,
    "2021-07":16.00,"2021-08":15.92,"2021-09":16.36,"2021-10":17.04,"2021-11":18.79,"2021-12":19.88,
    "2022-01":23.09,"2022-02":24.00,"2022-03":24.82,"2022-04":25.31,"2022-05":24.99,"2022-06":25.83,
    "2022-07":25.79,"2022-08":24.81,"2022-09":24.63,"2022-10":24.96,"2022-11":23.30,"2022-12":22.12,
    "2023-01":20.01,"2023-02":18.86,"2023-03":18.38,"2023-04":17.95,"2023-05":18.10,"2023-06":18.26,
    "2023-07":18.26,"2023-08":18.91,"2023-09":19.09,"2023-10":21.49,"2023-11":20.87,"2023-12":19.23,
    "2024-01":19.39,"2024-02":19.85,"2024-03":20.09,"2024-04":20.11,"2024-05":20.50,"2024-06":21.08,
    "2024-07":21.31,"2024-08":21.58,"2024-09":22.29,"2024-10":20.90,"2024-11":21.12,"2024-12":20.74,
    "2025-01":20.73,"2025-02":19.90,"2025-03":18.21,"2025-04":17.92,"2025-05":18.13,"2025-06":18.30,
    "2025-07":18.89,"2025-08":18.50,"2025-09":16.17,"2025-10":14.30,"2025-11":13.89,"2025-12":13.64,
    "2026-01":13.55,
    "2026-02":16.29, // USDA AMS announced March 4, 2026
    // 2026-03: NOT YET ANNOUNCED
  }
};

// Given a quarter label like "Q1 2026", return the 3 month keys
function drpQuarterMonthKeys(qLabel) {
  const m = qLabel?.match(/Q(\d)\s+(\d{4})/);
  if (!m) return [];
  const q = parseInt(m[1]), y = parseInt(m[2]);
  const months = q===1?[1,2,3]:q===2?[4,5,6]:q===3?[7,8,9]:[10,11,12];
  return months.map(mo => y+"-"+(mo<10?"0":"")+mo);
}

// Returns { classIII, classIV, monthsKnownIII, monthsKnownIV, isPartial, isFuture }
// classIII/IV = quarterly average of known months (null if none known)
function drpGetQuarterActuals(qLabel, overrides) {
  const today = new Date();
  const keys = drpQuarterMonthKeys(qLabel);
  if (!keys.length) return null;

  // Determine if this quarter is entirely in the future
  // Quarter's first month key
  const firstKey = keys[0];
  const [fy, fm] = firstKey.split("-").map(Number);
  const firstOfQuarter = new Date(fy, fm-1, 1);
  // If quarter hasn't started yet, it's future — no indemnity
  if (firstOfQuarter > today) return { isFuture: true };

  const knownIII = keys.filter(k => (overrides?.[k]?.III ?? USDA_MONTHLY_PRICES.III[k]) != null);
  const knownIV  = keys.filter(k => (overrides?.[k]?.IV  ?? USDA_MONTHLY_PRICES.IV[k])  != null);
  const isPartial = knownIII.length < 3 || knownIV.length < 3;

  const avgIII = knownIII.length
    ? knownIII.reduce((s,k)=>s+(overrides?.[k]?.III ?? USDA_MONTHLY_PRICES.III[k]),0)/knownIII.length
    : null;
  const avgIV = knownIV.length
    ? knownIV.reduce((s,k)=>s+(overrides?.[k]?.IV ?? USDA_MONTHLY_PRICES.IV[k]),0)/knownIV.length
    : null;

  return { classIII: avgIII, classIV: avgIV,
           monthsKnownIII: knownIII.length, monthsKnownIV: knownIV.length,
           isPartial, isFuture: false };
}

// Per-month detail for a quarter — returns array of {key, label, classIII, classIV, sourceIII, sourceIV}
// source is "announced" | "estimated" | "pending"
function drpGetMonthDetail(qLabel, overrides, cmeFutures) {
  const keys = drpQuarterMonthKeys(qLabel);
  if (!keys.length) return [];
  const today = new Date();
  const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return keys.map(k => {
    const [y, m] = k.split("-").map(Number);
    const label = MONTH_NAMES[m-1] + " " + y;
    // Check USDA announced (overrides first, then hardcoded)
    const usdaIII = overrides?.[k]?.III ?? USDA_MONTHLY_PRICES.III[k] ?? null;
    const usdaIV  = overrides?.[k]?.IV  ?? USDA_MONTHLY_PRICES.IV[k]  ?? null;
    // Check CME futures estimates
    const cmeIII  = cmeFutures?.III?.[k] ?? null;
    const cmeIV   = cmeFutures?.IV?.[k]  ?? null;
    // Month is in the future if we're before or in that month and USDA hasn't announced yet
    const monthEnd = new Date(y, m, 0); // last day of month
    const isPast = today > monthEnd;
    // Who estimated an unannounced month: RMA's futures-implied price, or the
    // daily SILAS market estimate (src/drpMonthEstimate.js) for a month RMA no
    // longer publishes. Announced always wins above.
    const estKind = (cls, v, usda) => usda != null || v == null ? null : cmeFutures?.silas?.[cls]?.[k] ? "silas" : "rma";
    return {
      key: k,
      label,
      classIII: usdaIII ?? cmeIII ?? null,
      classIV:  usdaIV  ?? cmeIV  ?? null,
      sourceIII: usdaIII != null ? "announced" : cmeIII != null ? "estimated" : "pending",
      sourceIV:  usdaIV  != null ? "announced" : cmeIV  != null ? "estimated" : "pending",
      estKindIII: estKind("III", cmeIII, usdaIII),
      estKindIV:  estKind("IV", cmeIV, usdaIV),
      carriedForward: !!cmeFutures?.estMeta?.[k]?.carriedForward,
      futuresBased: !!cmeFutures?.estMeta?.[k]?.futures,
      isPast,
    };
  });
}

// Enhanced quarter actuals that blends USDA announced + CME futures for full quarter estimates
function drpGetQuarterActualsEnhanced(qLabel, overrides, cmeFutures) {
  const detail = drpGetMonthDetail(qLabel, overrides, cmeFutures);
  if (!detail.length) return null;
  const today = new Date();
  const [fy, fm] = detail[0].key.split("-").map(Number);
  const firstOfQuarter = new Date(fy, fm-1, 1);
  const isFuture = firstOfQuarter > today;

  const withIII = detail.filter(d => d.classIII != null);
  const withIV  = detail.filter(d => d.classIV  != null);
  const announcedIII = detail.filter(d => d.sourceIII === "announced");
  const announcedIV  = detail.filter(d => d.sourceIV  === "announced");
  const estimatedIII = detail.filter(d => d.sourceIII === "estimated");
  const estimatedIV  = detail.filter(d => d.sourceIV  === "estimated");

  const avgIII = withIII.length ? withIII.reduce((s,d)=>s+d.classIII,0)/withIII.length : null;
  const avgIV  = withIV.length  ? withIV.reduce((s,d)=>s+d.classIV,0)/withIV.length    : null;

  const isPartial = withIII.length < 3 || withIV.length < 3;
  const hasEstimates = estimatedIII.length > 0 || estimatedIV.length > 0;
  const isFullyEstimated = announcedIII.length === 0 && announcedIV.length === 0;
  const silasMonths = detail.filter(d => d.estKindIII === "silas" || d.estKindIV === "silas");
  const rmaEstimated = detail.some(d => d.estKindIII === "rma" || d.estKindIV === "rma");

  return {
    silasEstimated: silasMonths.length > 0, rmaEstimated,
    estWord: silasMonths.length && rmaEstimated ? "est." : silasMonths.length ? "market est." : "RMA",
    estAsOf: silasMonths.length ? (cmeFutures?.estAsOf || null) : null,
    carriedMonths: silasMonths.filter(d => d.carriedForward).map(d => d.key),
    futuresMonths: silasMonths.filter(d => d.futuresBased).map(d => d.key),
    classIII: avgIII, classIV: avgIV,
    monthsKnownIII: announcedIII.length, monthsKnownIV: announcedIV.length,
    monthsEstIII: estimatedIII.length, monthsEstIV: estimatedIV.length,
    monthsTotalIII: withIII.length, monthsTotalIV: withIV.length,
    isPartial, isFuture: isFuture && !hasEstimates,
    hasEstimates, isFullyEstimated,
    detail,
  };
}

// Fetch RMA's monthly futures-implied Class III/IV for not-yet-announced months
// from /api/drp-quote?forward=1 (the m_class_iii/m_class_iv arrays RMA publishes
// daily). Shaped like the old manual-CME object ({III:{key:price}, IV:{key:price},
// updated, source}) so the indemnity estimator consumes it with no other change —
// replacing the manual CME entry that used to fill these months.
async function fetchRmaForwardPrices() {
  try {
    const r = await fetch("/api/drp-quote?forward=1").then(x => x.json());
    if (!r?.ok) return null;
    const out = { III: {}, IV: {}, updated: r.sales_date, source: r.source || "RMA" };
    for (const [k, v] of Object.entries(r.months || {})) {
      if (v.III != null) out.III[k] = v.III;
      if (v.IV != null) out.IV[k] = v.IV;
    }
    return out;
  } catch { return null; }
}

// The daily SILAS estimate of each unannounced month (drp_month_estimates, sql/107,
// written by ams-sync datasets=drpest; the rule is src/drpMonthEstimate.js). Market
// data — authenticated read, like class_prices. Fails soft: before sql/107 is
// applied, or on any error, this returns null and unpriced months stay unpriced.
async function fetchDrpMonthEstimates() {
  try {
    const since = new Date(Date.now() - 21 * 864e5).toISOString().slice(0, 10);
    const r = await fetch(`${SUPABASE_URL}/rest/v1/drp_month_estimates?select=id,estimate_date,month_key,milk_class,price,carried_forward,model_version,basis&estimate_date=gte.${since}&order=id.asc`, { headers: getAuthHeaders() });
    if (!r.ok) return null;
    const rows = await r.json();
    return Array.isArray(rows) && rows.length ? drpLatestMonthEstimates(rows) : null;
  } catch { return null; }
}

// ⚠ THE PROTECTED PRICE IS THE AIP'S GUARANTEE PER CWT, NOT A ROUNDED COPY OF IT.
// The syncs stored trigger_price as toFixed(2) of (M13 P18 expected revenue
// guarantee ÷ cwt), and JavaScript's toFixed rounds 18.715 down to 18.71, so a
// $374,300 guarantee on 20,000 cwt read $18.71 where the AIP's own figure is
// $18.715. When the stored price is that rounded copy (within a cent of the
// exact quotient) the exact one is used; any other stored price is left alone.
function drpExactProtectedPrice(row) {
  const stored = row && row.trigger_price != null && row.trigger_price !== "" ? Number(row.trigger_price) : null;
  const g = Number(row && row.exp_rev_guar), lbs = Number(row && row.declared_milk_prod);
  if (!(g > 0) || !(lbs > 0)) return stored;
  const exact = Math.round((g / (lbs / 100)) * 1e4) / 1e4;
  if (stored == null || !Number.isFinite(stored)) return exact;
  return Math.abs(exact - stored) < 0.01 ? exact : stored;
}
// A per-cwt price as printed: cents, unless the AIP's figure carries a real
// half cent (or finer), which is shown rather than rounded away.
function drpPriceCwtText(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  const dec = Math.abs(n * 100 - Math.round(n * 100)) > 1e-6 ? 3 : 2;
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Protection factor helper — use explicit value if stored, else derive from liability/expRevGuar.
// liability = expRevGuar × PF, so PF = liability / expRevGuar (exact for Sparta 1.5).
function drpGetProtectionFactor(end) {
  if (end.protectionFactor && Number(end.protectionFactor) > 1.05) return Number(end.protectionFactor);
  const guar = Number(end.expRevGuar)||0;
  const liab = Number(end.liability)||0;
  if (guar > 0 && liab > 0) {
    const derived = liab / guar;
    if (derived > 1.05) return Math.round(derived * 100) / 100;
  }
  return 1.0;
}

// Per-endorsement indemnity. Returns dollars or null.
//
// THE YIELD ADJUSTMENT FACTOR IS RMA'S, NOT OURS (2026-10-20). M13 Handbook P28-1
// (Plan 83): YAF = round(actual yield ÷ expected yield, 4), BOTH published by RMA
// in ADM A00832 per milk yield ID — the ID the endorsement was sold under on its
// own sales date (A00833). This used to divide a NASS 3-month milk-per-cow sum by
// the endorsement's `exp_milk_per_cow`, which is NULL on 194 of 273 endorsements
// (all of Sparta's), so most quarters carried no YAF at all: Sparta Q1 2026 read
// $358,380 against $397,035 paid. The rule lives in src/drpYieldAdjust.js; each
// endorsement arrives here with `yieldAdj` resolved (drpLoadYieldContext):
//   final   -> RMA's YAF;   pending -> 1, labelled "pending (RMA publishes ~…)";
//   unknown -> 1, and the card says the adjustment is unknown and not applied.
// Dollars follow P28-1 too: the AIP's revenue guarantee, and — once all three
// months of the class are announced — RMA's cent/dollar rounding, so a settled
// quarter reproduces the paid figure (tools/drp-settlement-tieout.js).
// The Class III share of an endorsement's milk under the CLASS pricing option,
// from its stored class_iii_pct (M13 P18 #29 x 100) when that is a true blend
// (1-99), else 1 or 0 from its class. COMPONENT pricing (practice 832, stored as
// type "Component") returns null: it is priced from butterfat, protein and other
// solids prices that Silas does not load, so it is never priced as a class.
function drpClassIIIWeight(end) {
  if (end && String(end.type || "").toLowerCase() === "component") return null;
  const pct = end && end.classIIIPct != null && end.classIIIPct !== "" ? Number(end.classIIIPct) : null;
  if (pct != null && Number.isFinite(pct) && pct > 0 && pct < 100) return pct / 100;
  if (end && (end.classIII === 1 || end.classIII === true)) return 1;
  if (end && (end.classIII === 0 || end.classIII === false)) return 0;
  return null;
}

function drpCalcIndemnity(end, actuals) {
  if (!actuals || actuals.isFuture) return null;
  // ⚠ A BLEND IS PRICED AS A BLEND. RMA's class pricing option prices the milk at
  // weight x Class III + (1 - weight) x Class IV; a 40/60 endorsement used to be
  // priced as all Class III, and a component-priced one fell through to Class IV.
  // Pure III and pure IV endorsements (every one on the book today) price exactly
  // as before.
  const w = drpClassIIIWeight(end);
  if (w == null) return null;
  const actual = w === 1 ? actuals.classIII
    : w === 0 ? actuals.classIV
    : (actuals.classIII == null || actuals.classIV == null) ? null
    : w * Number(actuals.classIII) + (1 - w) * Number(actuals.classIV);
  if (actual == null) return null;
  const adj = end.yieldAdj;
  const yaf = adj && adj.status === "final" ? adj.yaf : 1;
  const settled = w === 1 ? actuals.monthsKnownIII === 3
    : w === 0 ? actuals.monthsKnownIV === 3
    : actuals.monthsKnownIII === 3 && actuals.monthsKnownIV === 3;
  return drpP28Indemnity({ guarantee: drpRevenueGuarantee(end), lbs: Number(end.declaredMilkProd) || 0,
    share: end.insShr, protectionFactor: drpGetProtectionFactor(end), actualPrice: actual, yaf, settled });
}

// ONE rule for every DRP estimated-indemnity surface (screen chip, per-class
// rows, printed summary). An estimate exists only if at least one endorsement
// in the set could be PRICED. drpCalcIndemnity returns null for an endorsement
// whose class has no announced or RMA-estimated month yet; summing those as 0
// printed "No trigger" on a quarter nobody had evaluated (Sparta Q4 2026,
// ~$57k in the money at RMA's last expected price). Returns null for a future
// quarter (unchanged), else {amount, priced, unpriced, partial}; amount is
// null — never 0 — when nothing could be priced. `prep` attaches each
// endorsement's RMA yield adjustment (drpWithYieldAdj).
function drpEstimateIndemnity(rows, actuals, prep) {
  if (!actuals || actuals.isFuture) return null;
  let amount = 0, priced = 0, unpriced = 0;
  for (const e of rows || []) {
    const v = drpCalcIndemnity(prep ? prep(e) : e, actuals);
    if (v == null) { unpriced += 1; continue; }
    priced += 1;
    amount += v;
  }
  return { amount: priced > 0 ? amount : null, priced, unpriced, partial: priced > 0 && unpriced > 0 };
}

// What the screen and the printed summary need to resolve each endorsement's RMA
// milk yield ID: the A00832 rows for the policies' states and the A00833 rows for
// the endorsements' own sales dates (both RMA market data, authenticated read).
// Fails soft: before sql/108 adds actual_yield the read retries without it and
// every endorsement reads "pending (actual yield not loaded yet)", never final.
async function drpLoadYieldContext(ends, fallbackState) {
  const empty = { priceRows: [], yieldById: {}, fallbackState, actualColumn: false };
  try {
    const states = [...new Set((ends || []).map(e => drpStateFipsOf(e.policyState || fallbackState)).filter(Boolean))];
    const dates = [...new Set((ends || []).map(e => String(e.effectiveDate || "").slice(0, 10)).filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d)))];
    if (!states.length || !dates.length) return empty;
    const base = "milk_yield_id,state_code,reinsurance_year,expected_yield";
    let actualColumn = true, yrows;
    try {
      yrows = await sbFetch("drp_adm_yield", { select: base + ",actual_yield,actual_released_date", filters: [`state_code=in.(${states.join(",")})`], order: "milk_yield_id.asc" });
    } catch (_) {
      actualColumn = false;
      yrows = await sbFetch("drp_adm_yield", { select: base, filters: [`state_code=in.(${states.join(",")})`], order: "milk_yield_id.asc" });
    }
    const yieldById = {};
    for (const y of yrows || []) yieldById[y.milk_yield_id] = y;
    const ids = Object.keys(yieldById);
    if (!ids.length) return { ...empty, actualColumn };
    const priceRows = await sbFetch("drp_adm_daily_price", {
      select: "sales_date,milk_yield_id,quarter_code,exp_class_iii,exp_class_iv",
      filters: [`sales_date=in.(${dates.join(",")})`, `milk_yield_id=in.(${ids.join(",")})`],
      order: "sales_date.asc,milk_yield_id.asc",
    });
    // A00834 actual Class III/IV per Actual Price ID — settles a price tie between
    // two quarters (src/drpYieldAdjust.js). Missing before sql/108: no tie-break.
    const actualPriceById = {};
    const codes = [...new Set((priceRows || []).map(r => r.quarter_code).filter(v => v != null))];
    if (codes.length) {
      try {
        const ap = await sbFetch("drp_adm_actual_price", { select: "actual_price_id,actual_class_iii,actual_class_iv", filters: [`actual_price_id=in.(${codes.join(",")})`], order: "actual_price_id.asc" });
        for (const a of ap || []) actualPriceById[a.actual_price_id] = a;
      } catch (_) { /* table absent before sql/108 */ }
    }
    return { priceRows: priceRows || [], yieldById, actualPriceById, fallbackState, actualColumn };
  } catch (_) { return empty; }
}
// Attach each endorsement's RMA yield adjustment (the prep hook for drpEstimateIndemnity).
// `actuals` is the quarter's drpGetQuarterActualsEnhanced result: once settled, its
// rounded Class III/IV is what breaks a same-price tie between two quarters.
function drpWithYieldAdj(e, yctx, actuals) {
  return { ...e, yieldAdj: drpResolveYieldAdj(e, { ...(yctx || { priceRows: [], yieldById: {} }), quarterActual: drpQuarterActualOf(actuals) }) };
}

// Why a started quarter has no estimate: the first month of the quarter with
// no USDA announced price and no RMA estimate. The date printed is AMS's own
// scheduled release for that month (src/calendar/usdaReleaseCalendar.js, from the
// two-year AMS release PDFs) — e.g. October 2026 prices: Nov 4, 2026, not "on or
// before the 5th". Past the posted schedule it says so, never a guessed day.
function drpUnpricedReason(actuals, cls) {
  const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const isPend = (d) => cls === "III" ? d.sourceIII === "pending"
    : cls === "IV" ? d.sourceIV === "pending"
    : (d.sourceIII === "pending" || d.sourceIV === "pending");
  const first = (actuals?.detail || []).find(isPend);
  if (!first) return "No Class price available for this quarter yet";
  const [y, m] = String(first.key).split("-").map(Number);
  if (!y || !m) return "Awaiting USDA's first announced month";
  return `Awaiting USDA's ${MON[m-1]} ${y} announced price (${amsClassReleaseText(y, m)})`;
}

function drpSortKey(lbl) {
  const m = lbl?.match(/Q(\d)\s+(\d{4})/);
  return m ? parseInt(m[2])*10+parseInt(m[1]) : 0;
}
function drpFmtM(n) {
  const v = Number(n)||0;
  return v >= 1e6 ? (v/1e6).toFixed(1)+"M" : (v/1000).toFixed(0)+"K";
}
function drpWA(rows, key) {
  const w = rows.reduce((s,e)=>s+(Number(e.declaredMilkProd)||0),0);
  return w ? rows.reduce((s,e)=>s+(Number(e[key])||0)*(Number(e.declaredMilkProd)||0),0)/w : null;
}
function drpSum(rows, key) { return rows.reduce((s,e)=>s+(Number(e[key])||0),0); }

function DRPView({ farmer, isMobile, onReset, onUsdaPriceUpdate }) {
  const [tab, setTab]         = useState("summary");
  const [openQ, setOpenQ]     = useState({});
  const [filterQ, setFilterQ] = useState("all");
  const [resetting, setResetting] = useState(false);
  const [priceOverrides] = useState({});   // no manual USDA override any more — class_prices hydrates USDA_MONTHLY_PRICES
  const [drpEstProd, setDrpEstProd] = useState({});
  const [showAllCov, setShowAllCov] = useState(false);   // Summary: active-only by default
  const [filterFrom, setFilterFrom] = useState("");      // All Endorsements: purchase-date range
  const [filterTo, setFilterTo]     = useState("");
  const [sortCol, setSortCol]       = useState(null);    // All Endorsements: 'date' | 'quarter' | null
  const [sortDir, setSortDir]       = useState("asc");
  const [actualClaims, setActualClaims] = useState([]);  // DRP indemnities (policy_type='drp') for the est→actual switch
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  // Load persisted est production values from DB
  useEffect(() => {
    if (!farmer?.id) return;
    // Seed immediately from in-memory farmer object if available
    if (farmer.metadata?.drpEstProd) {
      setDrpEstProd(farmer.metadata.drpEstProd);
    }
    // Always fetch fresh from DB to ensure cross-device sync
    fetch(`${SUPABASE_URL}/rest/v1/farmers?id=eq.${farmer.id}&select=metadata`, {
      headers: getAuthHeaders()
    })
      .then(r => r.json())
      .then(rows => {
        const val = rows?.[0]?.metadata?.drpEstProd;
        if (val) setDrpEstProd(val);
      })
      .catch(() => {});
  }, [farmer?.id]); // { "2026-03": {III:15.10,IV:14.20} }
  // Forward (not-yet-announced) Class III/IV months now come automatically from
  // RMA's daily futures-implied prices (drp-quote?forward=1) — no manual CME entry.
  // Same shape the estimator already consumes: {III:{key:price}, IV:{key:price}, updated, source}.
  const [cmeFutures, setCmeFutures] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchRmaForwardPrices().then(p => { if (alive && p) setCmeFutures(p); });
    return () => { alive = false; };
  }, []);
  // A quarter whose sales have closed has no RMA forward price; the daily SILAS
  // market estimate fills those months (RMA first, announced always wins).
  const [drpMonthEst, setDrpMonthEst] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchDrpMonthEstimates().then(e => { if (alive && e) setDrpMonthEst(e); });
    return () => { alive = false; };
  }, []);
  const fwdPrices = useMemo(() => drpMergeForwardPrices(cmeFutures, drpMonthEst), [cmeFutures, drpMonthEst]);
  const fwdPrev   = useMemo(() => drpMergeForwardPrices(cmeFutures, drpMonthEst, true), [cmeFutures, drpMonthEst]);

  // ── RMA YIELD ADJUSTMENT (src/drpYieldAdjust.js) ──
  // Each endorsement's YAF is RMA's: the expected AND actual yield of the milk
  // yield ID it was sold under (A00832 via drp_adm_yield; the ID named by A00833
  // for its own sales date via drp_adm_daily_price). Replaces a NASS milk-per-cow
  // sum over an `exp_milk_per_cow` that is NULL on most endorsements.
  const yafState = (farmer.state || "NY").toUpperCase();
  const drpEndsKey = (farmer.drpPolicies || []).flatMap(p => (p.endorsements || []).map(e => `${e.policyState || ""}|${e.effectiveDate || ""}`)).sort().join(",");
  const [drpYieldCtx, setDrpYieldCtx] = useState(null);
  useEffect(() => {
    let alive = true;
    const ends = (farmer.drpPolicies || []).flatMap(p => p.endorsements || []);
    drpLoadYieldContext(ends, yafState).then(c => { if (alive) setDrpYieldCtx(c); });
    return () => { alive = false; };
  }, [drpEndsKey, yafState]);
  const yctxView = drpYieldCtx || { priceRows: [], yieldById: {}, fallbackState: yafState };
  // Expected milk per cow shown in the endorsement table: RMA's expected yield for
  // the endorsement's own milk yield ID, else the value stored on the endorsement.
  const mpcOf = (e) => {
    const a = drpWithYieldAdj(e, yctxView, drpGetQuarterActualsEnhanced(e.quarter, priceOverrides, fwdPrices)).yieldAdj;
    if (a && a.expectedYield > 0) return a.expectedYield;
    const v = e?.expMilkPerCow;
    return v != null && v !== "" && Number(v) > 0 ? Number(v) : null;
  };

  // ── ACTUAL DRP INDEMNITIES (claims data) for the est→actual switch ──
  // DRP claims live in `indemnities` (policy_type='drp'), carrying only
  // policy_id + crop_year + claim_number + check_date + amount — NO quarter
  // attribution. We widen to every policy this farmer owns (mirrors
  // FarmerClaimsHistory) then attribute each paid claim to a quarter in
  // actualByQuarter below. Once a quarter has a paid claim, its card shows the
  // ACTUAL paid figure instead of the estimate.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const farmerDbIds = farmer.allFarmerDbIds || farmer._allFarmerIds || [farmer.id].filter(Boolean);
        let policyIds = farmer.allPolicyIds || farmer._allPolicyIds || [];
        if (farmerDbIds.length) {
          const pols = await sbFetch("policies", {
            filters: [`farmer_id=in.(${farmerDbIds.join(",")})`], select: "id",
          }).catch(() => []);
          policyIds = Array.from(new Set([...policyIds, ...(pols || []).map(p => p.id).filter(Boolean)]));
        }
        if (!policyIds.length) return;
        const rows = await sbFetch("indemnities", {
          filters: [`policy_id=in.(${policyIds.join(",")})`, "policy_type=eq.drp"],
          select: "policy_id,crop_year,claim_number,indemnity_amount,claim_total,status,check_date,loss_code,quarter",
        }).catch(() => []);
        if (alive) setActualClaims(rows || []);
      } catch (_) { /* best-effort — estimates still render */ }
    })();
    return () => { alive = false; };
  }, [farmer.id]);

  // Helper: get Class III/IV price for a month key, checking overrides first
  function getMilkPrice(cls, monthKey) {
    if (priceOverrides[monthKey]?.[cls] != null) return priceOverrides[monthKey][cls];
    return USDA_MONTHLY_PRICES[cls][monthKey] ?? null;
  }


  if (!farmer.drpPolicies?.length) {
    return <Card style={{textAlign:"center",padding:"40px",color:"#60735f"}}>No DRP policies on file.</Card>;
  }

  // Derive quarter from endDate — parse manually to avoid UTC/local timezone shifts
  function deriveQuarter(e) {
    // If the quarter column already has a proper "Q# YYYY" format, use it directly
    // (M13 imports store this correctly)
    if (e.quarter && /^Q[1-4]\s+\d{4}$/.test(String(e.quarter).trim())) {
      return String(e.quarter).trim();
    }
    // Fall back to deriving from endDate (legacy SOI imports)
    if (!e.endDate) return "Unknown";
    // Handles "2026-03-31" (ISO) and "03/31/2026" (MM/DD/YYYY)
    let m, y;
    const iso = String(e.endDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const mdy = String(e.endDate).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (iso)      { y = parseInt(iso[1]); m = parseInt(iso[2]); }
    else if (mdy) { y = parseInt(mdy[3]); m = parseInt(mdy[1]); }
    else return "Unknown";
    const q = m <= 3 ? 1 : m <= 6 ? 2 : m <= 9 ? 3 : 4;
    return "Q" + q + " " + y;
  }

  const allEnds = (farmer.drpPolicies||[]).flatMap(p =>
    (p.endorsements||[]).map(e => ({
      ...e,
      quarter: deriveQuarter(e),
      // the DRP crop year (AIP's own, else the July-1 rule) and the policy it sits
      // under — the season summary and "awaiting payment" are per crop year
      cropYear: e.cropYear || deriveDrpCropYear(e.effectiveDate),
      _policyNumber: p.policyNumber || farmer.policyNumber || null,
    }))
  );
  // Claim ESTIMATES are an internal admin tool (Kevin, 2026-10-18): effective
  // identity, so preview-as and the portal never see one. Actual paid claims,
  // prices and coverage still render for everyone.
  const showEst = isCurrentUserAdmin();

  const totEnds = allEnds.length;
  const totLiab = drpSum(allEnds,"liability");
  const totProd = drpSum(allEnds,"producerPremium");
  const totMilk = drpSum(allEnds,"declaredMilkProd");
  const totPrem = drpSum(allEnds,"totalPremium");

  // Build quarter map
  const qMap = {};
  allEnds.forEach(e => {
    const k = e.quarter || "Unknown";
    if (!qMap[k]) qMap[k] = [];
    qMap[k].push(e);
  });
  const quarters = Object.keys(qMap).sort((a,b) => drpSortKey(a)-drpSortKey(b));

  // ── Active vs ended (Summary tab defaults to active-only) ──────────────────
  // An endorsement is "active" until its quarter end date passes. The Summary
  // tab shows active coverages by default; the "Show all" checkbox reveals
  // ended quarters (which is where settled/Actual indemnities live).
  const _todayISO = new Date().toISOString().slice(0, 10);
  const toISO = (d) => {
    if (!d) return null;
    const s = String(d);
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mdy) return `${mdy[3]}-${mdy[1].padStart(2, "0")}-${mdy[2].padStart(2, "0")}`;
    return null;
  };
  // Quarter end from a "Q# YYYY" label — DRP quarters are calendar quarters.
  // This is the robust active/ended signal: end_date is NULL on some import
  // paths (e.g. NAU-loaded CY2026 endorsements), which previously made past
  // quarters wrongly read as "active" AND split a calendar quarter's pounds
  // (only the null-end_date crop year showed). The quarter label is always
  // present, so derive the cutoff from it; fall back to end_date only if the
  // label can't be parsed.
  // One rule (src/drpCardRules.js) for "active": the header, the stat cards and
  // the toggle all count the same endorsements.
  const quarterEndISO = drpQuarterEndIso;
  const isActiveEnd = (e) => drpIsActiveEnd(e, _todayISO); // truly unknown → treat as active
  const covTot = drpCoverageTotals(allEnds, _todayISO);
  const activeCount = covTot.active.count;

  // ── Actual-claim attribution by quarter ───────────────────────────────────
  // Each DRP claim (grouped by policy+crop_year+claim_number) is attributed to
  // the calendar quarter that settled it: the latest-ending quarter whose end
  // date precedes the claim's first check. Attribution is by SETTLEMENT TIMING
  // only — NOT by crop year. The SAME calendar quarter (e.g. Q1 2026) can be
  // covered by endorsements from two DRP reinsurance years (CY2025 sold before
  // Jul 2025 + CY2026 sold after), and a check from each must roll into that
  // one quarter's Actual. Because a quarter accumulates every matched claim,
  // both crop years' Q1-2026 checks sum into the single Q1 2026 figure. DRP
  // pays at most once per quarter per class. Quarter metadata comes from ALL
  // endorsements (not the active-filtered set) so ended quarters still resolve.
  const qMeta = {}; // "Q# YYYY" -> { endISO }
  allEnds.forEach(e => {
    const k = e.quarter;
    if (!k || qMeta[k]) return;
    qMeta[k] = { endISO: quarterEndISO(k) || toISO(e.endDate) };
  });
  const actualByQuarter = (() => {
    const byClaim = {};
    for (const c of actualClaims) {
      // crop_year in the key keeps a claim_number reused across crop years
      // distinct, but both still attribute to the same quarter
      const key = `${c.policy_id}|${c.crop_year || ""}|${c.claim_number || ""}`;
      if (!byClaim[key]) byClaim[key] = { amount: 0, primaryDate: null, primaryAmt: -Infinity, earliest: null, cropYear: c.crop_year, status: c.status, num: c.claim_number, quarter: c.quarter || null, dates: [] };
      const g = byClaim[key];
      if (!g.quarter && c.quarter) g.quarter = c.quarter; // authoritative quarter (M13 P28) if any row carries it
      const amt = Number(c.indemnity_amount) || 0;
      g.amount += amt;
      const cd = c.check_date ? String(c.check_date).slice(0, 10) : null;
      if (cd) {
        g.dates.push(cd);
        if (!g.earliest || cd < g.earliest) g.earliest = cd;
        if (amt > g.primaryAmt) { g.primaryAmt = amt; g.primaryDate = cd; }
      }
    }
    const out = {}; // q -> { amount, claims:[...] }
    const qEntries = Object.entries(qMeta).filter(([, m]) => m.endISO);
    for (const cl of Object.values(byClaim)) {
      if (!(cl.amount > 0)) continue;
      let q, authoritative = false;
      if (cl.quarter) {
        // AUTHORITATIVE: quarter resolved from M13 P28 claim lines → P18
        // endorsement interval (penny-exact, no date guesswork). Handles two
        // crop years settling the same calendar quarter for free.
        q = cl.quarter;
        authoritative = true;
      } else {
        // FALLBACK (no P28 detail, e.g. R&H or pre-backfill): infer the
        // settled quarter from the primary (largest) disbursement's check
        // date — the latest quarter that ended on/before it.
        const anchor = cl.primaryDate || cl.earliest;
        let cands = qEntries.filter(([, m]) => !anchor || m.endISO <= anchor);
        if (!cands.length) cands = qEntries.slice();
        if (!cands.length) continue;
        cands.sort((a, b) => (a[1].endISO || "").localeCompare(b[1].endISO || ""));
        q = cands[cands.length - 1][0];
      }
      if (!out[q]) out[q] = { amount: 0, claims: [] };
      out[q].amount += cl.amount;
      out[q].claims.push({ num: cl.num, amount: cl.amount, earliest: cl.primaryDate || cl.earliest, status: cl.status, cropYear: cl.cropYear, authoritative, dates: cl.dates });
    }
    return out;
  })();

  // ── Quarter status per (quarter × DRP crop year) — src/drpCardRules.js ──────
  // An ENDED quarter with no paid claim and an estimate still owed (in the money,
  // not priced yet, or RMA's yield adjustment pending) stays on the main list as
  // "Ended — awaiting payment". Once its claim is paid it moves back behind
  // "Show all coverages" with its Actual Paid. Admin only: in the portal, a
  // quarter "awaiting payment" would itself be a claim estimate.
  const qStatus = {}; // q -> { status, cells:[{cropYear, policies, paid, est, yafPending}], actuals }
  for (const q of quarters) {
    const rowsQ = qMap[q] || [];
    const actualsQ = drpGetQuarterActualsEnhanced(q, priceOverrides, fwdPrices);
    const prepQ = (e) => drpWithYieldAdj(e, yctxView, actualsQ);
    const ended = !rowsQ.some(isActiveEnd);
    const claimsQ = actualByQuarter[q]?.claims || [];
    const cyList = [...new Set(rowsQ.map(e => e.cropYear).filter(v => v != null))].sort();
    const cells = cyList.map(cy => {
      const rowsC = rowsQ.filter(e => e.cropYear === cy);
      const paidC = claimsQ.filter(c => Number(c.cropYear) === Number(cy));
      return {
        cropYear: cy,
        policies: [...new Set(rowsC.map(e => e._policyNumber).filter(Boolean))],
        paid: paidC.length ? paidC.reduce((s, c) => s + c.amount, 0) : null,
        est: drpEstimateIndemnity(rowsC, actualsQ, prepQ),
        yafPending: drpQuarterYieldSummary(rowsC.map(prepQ)).pendingN,
      };
    });
    qStatus[q] = { status: drpQuarterStatus({ ended, cells }), cells, actuals: actualsQ };
  }
  const summaryQuarters = quarters.filter(q => drpQuarterVisible(qStatus[q].status, { showAll: showAllCov, showEstimates: showEst }));
  const summaryEnds = allEnds.filter(e => summaryQuarters.includes(e.quarter || "Unknown"));
  const sQMap = {};
  summaryEnds.forEach(e => { const k = e.quarter || "Unknown"; (sQMap[k] = sQMap[k] || []).push(e); });
  const sQuarters = Object.keys(sQMap).sort((a, b) => drpSortKey(a) - drpSortKey(b));
  // The stat cards follow the header: ACTIVE coverage unless "Show all" is on.
  const sTotMilk = showAllCov ? covTot.all.lbs : covTot.active.lbs;
  const sTotLiab = showAllCov ? covTot.all.liability : covTot.active.liability;
  const awaitingQuarters = summaryQuarters.filter(q => qStatus[q].status.state === "awaiting");
  const awaitingCount = allEnds.filter(e => !isActiveEnd(e) && awaitingQuarters.includes(e.quarter)).length;
  const endedCount  = allEnds.length - activeCount;
  const hiddenCount = endedCount - awaitingCount;
  // Season summary per DRP crop year (admin): premium, paid, still to come, net.
  const seasonRows = showEst ? drpSeasonSummary({
    ends: allEnds,
    claims: Object.values(actualByQuarter).flatMap(x => x.claims).map(c => ({ cropYear: c.cropYear != null ? Number(c.cropYear) : null, amount: c.amount })),
    cells: quarters.flatMap(q => qStatus[q].cells.map(c => ({ ...c, state: qStatus[q].status.state }))),
  }) : [];
  const nextUsdaText = nextAmsClassReleaseText(usdaEtDateIso(new Date()));

  // ── DARK HEADER ──────────────────────────────────────────────────────────
  // Deduplicate policy numbers across all policies
  const policyNums = [...new Set(
    farmer.drpPolicies.map(p => p.policyNumber||farmer.policyNumber).filter(Boolean)
  )];
  const policyLine = policyNums.join("  ·  ") || farmer.policyNumber || "";

  return (
    <div>
      {/* ── HEADER ── */}
      <div style={{background:"#1a2e1a",borderRadius:10,padding:isMobile?"18px 16px":"24px 28px",marginBottom:16}}>
        <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.14em",color:"#4a7a4a",textTransform:"uppercase",marginBottom:6}}>
          Dairy Revenue Protection
        </div>
        <h2 style={{margin:"0 0 4px",fontSize:isMobile?20:26,fontWeight:800,color:"#fff",fontFamily:"'Lora',serif"}}>
          {farmer.name}
        </h2>
        <div style={{fontSize:12,color:"#7aaa7a",marginBottom:16,display:"flex",gap:8,flexWrap:"wrap"}}>
          {farmer.address && <span>{farmer.address}</span>}
          {farmer.phone && <><span>·</span><span>{farmer.phone}</span></>}
          {(farmer.address || farmer.phone) && policyLine && <span>·</span>}
          {policyLine && <span style={{fontFamily:"'Source Sans 3',sans-serif"}}>{policyLine}</span>}
          {farmer.insurer && <><span>·</span><span>{farmer.insurer}{farmer.agency?" · "+farmer.agency:""}</span></>}
        </div>
        {/* Big stats */}
        <div style={{display:"flex",gap:isMobile?20:40,flexWrap:"wrap",marginBottom:16}}>
          {[
            {val:String(covTot.active.count),                      lbl:"ACTIVE ENDORSEMENTS"},
            {val:"$"+Number(covTot.active.liability).toLocaleString(), lbl:"ACTIVE LIABILITY"},
          ].map(s=>(
            <div key={s.lbl}>
              <div style={{fontSize:isMobile?20:26,fontWeight:800,color:"#fff",lineHeight:1}}>{s.val}</div>
              <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",color:"#4a7a4a",marginTop:3}}>{s.lbl}</div>
            </div>
          ))}
        </div>
        {/* Quarter range + class legend + reset button */}
        <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{fontSize:12,color:"#a8c8a8"}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:"#6aaa6a",display:"inline-block",marginRight:5}}/>
            All-time: {covTot.all.count} endorsements · ${Number(covTot.all.liability).toLocaleString()} liability · {quarters.length} quarters
            {quarters.length > 0 && " · " + quarters[0] + (quarters.length > 1 ? " – " + quarters[quarters.length-1] : "")}
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{background:"#1a4a7a",color:"#b8d4f8",fontSize:11,fontWeight:800,
              padding:"3px 12px",borderRadius:4,border:"2px solid #4a70aa"}}>III  Class III pricing</span>
            <span style={{background:"#2d4a2d",color:"#b8d8b8",fontSize:11,fontWeight:800,
              padding:"3px 12px",borderRadius:4,border:"2px solid #4a8a4a"}}>IV  Class IV pricing</span>
          </div>
          {onReset && isCurrentUserAdmin() && (
            <button
              onClick={async () => {
                if (!window.confirm("Clear all DRP data for " + farmer.name + " and re-upload? This cannot be undone.")) return;
                setResetting(true);
                await onReset(farmer.id, 'drp');
                setResetting(false);
              }}
              style={{marginLeft:"auto",padding:"5px 14px",background:"transparent",
                border:"1px solid #c05050",borderRadius:5,color:"#f8b8b8",fontSize:11,
                fontWeight:700,cursor:"pointer",letterSpacing:"0.06em",opacity:resetting?0.5:1}}>
              {resetting ? "Clearing…" : "↺ Reset DRP Data"}
            </button>
          )}
        </div>
      </div>

      {/* ── TABS ── */}
      <div style={{display:"flex",gap:2,marginBottom:20,borderBottom:"2px solid #e4ece4"}}>
        {[
          {key:"summary",icon:"📊",label:"Summary by Quarter"},
          {key:"plan",   icon:"🎯",label:"Protection Plan"},
          {key:"all",    icon:"📋",label:"All Endorsements ("+totEnds+")"},
          {key:"claims", icon:"💰",label:"Claims History"},
        ].map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{padding:"10px 20px",border:"none",background:"none",cursor:"pointer",
              borderBottom:tab===t.key?"3px solid #2d4a2d":"3px solid transparent",
              marginBottom:-2,color:tab===t.key?"#1a2e1a":"#60735f",
              fontWeight:tab===t.key?800:500,fontSize:isMobile?13:14,
              display:"flex",alignItems:"center",gap:6}}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* ── PROTECTION PLAN TAB ── */}
      {tab==="plan" && (
        <DRPProtectionPlanner farmer={farmer} isMobile={isMobile} drpEstProd={drpEstProd} allEnds={allEnds}
          onPersist={(next) => { setDrpEstProd(next); if (farmer?.id) { fetch(`${SUPABASE_URL}/rest/v1/farmers?id=eq.${farmer.id}`, { method: "PATCH", headers: { ...getAuthHeaders(), Prefer: "return=minimal" }, body: JSON.stringify({ metadata: { ...(farmer.metadata || {}), drpEstProd: next } }) }).catch(() => {}); } }} />
      )}

      {/* ── SUMMARY TAB ── */}
      {tab==="summary" && (
        <div>
          {/* 2 stat cards — milk and liability only */}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(2,1fr)",gap:12,marginBottom:20}}>
            {[
              {lbl:"TOTAL MILK COVERED", val:drpFmtM(sTotMilk)+" lbs", sub:showAllCov?"across all quarters":"active quarters"},
              {lbl:"TOTAL LIABILITY",    val:"$"+Number(sTotLiab).toLocaleString(), sub:showAllCov?"all endorsements":"active endorsements"},
            ].map(s=>(
              <div key={s.lbl} style={{background:"#fff",border:"1px solid #e4ece4",borderRadius:8,padding:"16px 18px"}}>
                <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",color:"#60735f",textTransform:"uppercase",marginBottom:8}}>{s.lbl}</div>
                <div style={{fontSize:isMobile?16:20,fontWeight:800,color:"#1a2e1a",fontFamily:"'Lora',serif"}}>{s.val}</div>
                <div style={{fontSize:11,color:"#aaa",marginTop:3}}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Active-only toggle — default shows coverages whose quarter hasn't ended */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10,marginBottom:16}}>
            <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#2d4a2d",cursor:"pointer",userSelect:"none",fontWeight:600}}>
              <input type="checkbox" checked={showAllCov} onChange={ev=>setShowAllCov(ev.target.checked)} style={{cursor:"pointer",width:16,height:16}} />
              Show all coverages
              <span style={{fontSize:11,color:"#60735f",fontWeight:500}}>
                ({showAllCov
                  ? `showing all ${allEnds.length} · ${activeCount} active, ${endedCount} ended`
                  : `showing ${activeCount} active${awaitingCount ? ` · ${awaitingCount} ended, awaiting payment` : ""} · ${hiddenCount} ended hidden`})
              </span>
            </label>
          </div>

          {/* Est. Production input card */}
          {(() => {
            const perQ = drpEstProd.__perQuarter === true;
            const globalVal = drpEstProd.__global || '';
            const globalNum = parseFloat((globalVal+'').replace(/,/g,''));
            function saveEstProd(next) {
              setDrpEstProd(next);
              if (farmer?.id) {
                const mergedMeta = { ...(farmer.metadata || {}), drpEstProd: next };
                fetch(`${SUPABASE_URL}/rest/v1/farmers?id=eq.${farmer.id}`, {
                  method: "PATCH",
                  headers: { ...getAuthHeaders(), Prefer: "return=minimal" },
                  body: JSON.stringify({ metadata: mergedMeta })
                }).catch(() => {});
              }
            }
            return (
              <div style={{background:"#fff",border:"1px solid #e4ece4",borderRadius:8,padding:"14px 18px",marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                  <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",color:"#60735f",textTransform:"uppercase"}}>Est. Quarterly Production</div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <label style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#60735f",cursor:"pointer",userSelect:"none"}}>
                      <input type="checkbox" checked={perQ}
                        onChange={ev => saveEstProd({...drpEstProd, __perQuarter: ev.target.checked})}
                        style={{cursor:"pointer"}}
                      />
                      Enter per quarter
                    </label>
                  </div>
                </div>
                {!perQ && (
                  <div style={{display:"flex",alignItems:"center",gap:12,marginTop:10}}>
                    <input
                      type="text"
                      placeholder="lbs per quarter"
                      value={globalVal}
                      onChange={ev => saveEstProd({...drpEstProd, __global: ev.target.value})}
                      style={{width:160,padding:"6px 10px",fontFamily:"'Source Sans 3',sans-serif",fontSize:13,textAlign:"right",
                        border:"1px solid #c8d8c8",borderRadius:5,background:"#f4f8f4",color:"#1a2e1a",outline:"none"}}
                    />
                    {globalNum > 0 && (
                      <span style={{fontSize:11,color:"#60735f"}}>applied to all {sQuarters.length} quarters</span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Season summary per DRP crop year — admin only (claim estimates) */}
          {showEst && seasonRows.length > 0 && (
            <div style={{background:"#fff",border:"1px solid #e4ece4",borderRadius:8,padding:"12px 18px",marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",color:"#60735f",textTransform:"uppercase",marginBottom:6}}>Season summary <span style={{fontWeight:600,letterSpacing:0,textTransform:"none"}}>· agent only</span></div>
              {seasonRows.map(r => (
                <div key={r.cropYear} style={{fontSize:12.5,color:"#1a2e1a",padding:"3px 0",display:"flex",flexWrap:"wrap",gap:"4px 14px"}}>
                  <strong>CY{r.cropYear}</strong>
                  <span>Producer premium ${Math.round(r.premium).toLocaleString()}</span>
                  <span>Claims paid ${Math.round(r.paid).toLocaleString()}</span>
                  <span>Est. still to come ~${Math.round(r.toCome).toLocaleString()}{r.unpriced ? ` (+${r.unpriced} quarter${r.unpriced===1?"":"s"} not priced yet)` : ""}</span>
                  <strong style={{color: r.net >= 0 ? "#2d6a1a" : "#a33"}}>Net {r.net >= 0 ? "+" : "−"}${Math.abs(Math.round(r.net)).toLocaleString()}</strong>
                </div>
              ))}
            </div>
          )}

          {/* Quarter cards */}
          {sQuarters.length === 0 && (
            <div style={{background:"#fff",border:"1px solid #e4ece4",borderRadius:8,padding:"28px 20px",textAlign:"center",color:"#60735f",fontSize:13}}>
              No active coverages.{hiddenCount > 0 && <> Check <strong>Show all coverages</strong> above to see {hiddenCount} ended endorsement{hiddenCount===1?"":"s"}.</>}
            </div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {sQuarters.map((q) => {
              const rows  = sQMap[q];
              const III   = rows.filter(e => e.classIII===1||e.classIII===true);
              const IV    = rows.filter(e => !(e.classIII===1||e.classIII===true));
              const IIIlbs = drpSum(III,"declaredMilkProd");
              const IVlbs  = drpSum(IV, "declaredMilkProd");
              const totlbs = IIIlbs + IVlbs;
              const qProd  = drpSum(rows,"producerPremium");
              const qc     = drpQColor(q);
              const isOpen = !!openQ[q];

              // Estimated indemnity from USDA AMS announced prices + RMA/market
              // estimates, adjusted by RMA's own yield adjustment factor per
              // endorsement (drpWithYieldAdj — final, pending or unknown).
              const actuals = drpGetQuarterActualsEnhanced(q, priceOverrides, fwdPrices);
              const withYieldAdj = (e) => drpWithYieldAdj(e, yctxView, actuals);
              const monthDetail = actuals?.detail || drpGetMonthDetail(q, priceOverrides, fwdPrices);
              const estQ = drpEstimateIndemnity(rows, actuals, withYieldAdj);
              const estIndemnity = estQ ? estQ.amount : null;
              const estIIIIndem = drpEstimateIndemnity(III, actuals, withYieldAdj)?.amount ?? null;
              const estIVIndem  = drpEstimateIndemnity(IV,  actuals, withYieldAdj)?.amount ?? null;
              // The day's change: the same quarter priced on the PREVIOUS estimate's
              // months — only when that day priced exactly the same months.
              const prevActuals = actuals?.silasEstimated ? drpGetQuarterActualsEnhanced(q, priceOverrides, fwdPrev) : null;
              const prevComparable = !!(prevActuals && fwdPrev.estAsOf && !prevActuals.isFuture
                && prevActuals.monthsTotalIII === actuals.monthsTotalIII && prevActuals.monthsTotalIV === actuals.monthsTotalIV);
              const prevIndemOf = (list) => prevComparable ? (drpEstimateIndemnity(list, prevActuals, withYieldAdj)?.amount ?? null) : null;
              const estNote = (cur, list) => { if (!actuals?.silasEstimated) return null; const prev = prevIndemOf(list);
                return drpDescribeEstimate({ asOf: actuals.estAsOf, prevAsOf: fwdPrev.estAsOf,
                  change: prev != null && cur != null ? cur - prev : null, carriedMonths: actuals.carriedMonths, futuresMonths: actuals.futuresMonths }); };

              // RMA yield adjustment across this quarter's endorsements: lb-weighted
              // YAF over the ones RMA has finalised, plus pending / unknown counts.
              const yieldSum = drpQuarterYieldSummary(rows.map(withYieldAdj));
              const yafAvg = yieldSum.yafAvg;
              const qs = qStatus[q];
              // Distance to trigger per class (admin): the quarter's expected class
              // price (announced → market est. → RMA futures) vs the lbs-weighted
              // trigger. Not on a quarter already fully paid.
              const yafNote = yieldSum.pendingN ? " · yield adjustment pending" : yafAvg != null ? ` · YAF ${yafAvg.toFixed(4)} (RMA) also applies` : "";
              const distLines = (showEst && actuals && !actuals.isFuture && !(qs.status.state === "paid"))
                ? [III.length ? drpTriggerDistanceText("III", drpTriggerDistance(III, actuals.classIII)) : null,
                   IV.length  ? drpTriggerDistanceText("IV",  drpTriggerDistance(IV,  actuals.classIV))  : null].filter(Boolean)
                : [];

              // Weighted averages
              const IIIavgCov   = drpWA(III,"coverageLevel");
              const IIIavgFull  = drpWA(III,"projectedPrice");   // full price (projected)
              const IIIavgTrig  = drpWA(III,"triggerPrice");     // trigger = projected * coverage
              const IIIavgPrem  = III.length ? drpSum(III,"producerPremium")/IIIlbs*100 : null;
              const IIInetFloor = (IIIavgTrig != null && IIIavgPrem != null)
                ? IIIavgTrig - IIIavgPrem : null;

              const IVavgCov    = drpWA(IV,"coverageLevel");
              const IVavgFull   = drpWA(IV,"projectedPrice");
              const IVavgTrig   = drpWA(IV,"triggerPrice");
              const IVavgPrem   = IV.length ? drpSum(IV,"producerPremium")/IVlbs*100 : null;
              const IVnetFloor  = (IVavgTrig != null && IVavgPrem != null)
                ? IVavgTrig - IVavgPrem : null;

              return (
                <div key={q} style={{background:"#fff",border:"1px solid #e4ece4",borderRadius:8,overflow:"hidden"}}>
                  {/* Quarter header row — always visible */}
                  <div
                    onClick={()=>setOpenQ(prev=>({...prev,[q]:!prev[q]}))}
                    style={{display:"flex",alignItems:"center",gap:isMobile?8:20,
                      padding:isMobile?"10px 12px":"14px 20px",cursor:"pointer",
                      background:isOpen?"#f4f8f4":"#fff",flexWrap:isMobile?"wrap":"nowrap",
                      overflowX:isMobile?"auto":"visible"}}
                  >
                    {/* Quarter badge */}
                    <span style={{background:qc.bg,color:qc.color,fontSize:13,fontWeight:800,
                      padding:"5px 14px",borderRadius:6,border:"1px solid "+qc.color+"33",
                      minWidth:90,textAlign:"center",flexShrink:0}}>{q}</span>

                    {/* Total lbs */}
                    <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?70:90}}>
                      <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#60735f",textTransform:"uppercase"}}>Total Lbs</span>
                      <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:700,fontSize:16,color:"#1a2e1a"}}>{drpFmtM(totlbs)}</span>
                    </div>

                    {/* Class III lbs */}
                    {IIIlbs > 0 && (
                      <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?60:80}}>
                        <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#1a4a7a",textTransform:"uppercase"}}>III Lbs</span>
                        <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:600,fontSize:14,color:"#1a4a7a"}}>{drpFmtM(IIIlbs)}</span>
                      </div>
                    )}

                    {/* Class IV lbs */}
                    {IVlbs > 0 && (
                      <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?60:80}}>
                        <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#2d5a2d",textTransform:"uppercase"}}>IV Lbs</span>
                        <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:600,fontSize:14,color:"#2d5a2d"}}>{drpFmtM(IVlbs)}</span>
                      </div>
                    )}

                    {/* Producer premium */}
                    <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?70:90}}>
                      <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#60735f",textTransform:"uppercase"}}>Producer Prem</span>
                      <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:600,fontSize:14,color:"#555"}}>{"$"+Number(qProd).toLocaleString()}</span>
                    </div>

                    {/* ACTUAL indemnity chip — shown once a claim is paid & matched to this quarter */}
                    {actualByQuarter[q] && (() => {
                      const qa = actualByQuarter[q];
                      const allAuth = qa.claims.every(c => c.authoritative);
                      const tip = qa.claims.map(c =>
                        `Claim ${c.num||"?"}: $${Math.round(c.amount).toLocaleString()}`
                        + (c.cropYear ? ` · CY${c.cropYear}` : "")
                        + (c.earliest ? ` (first check ${c.earliest})` : "")
                        + (c.status ? ` · ${c.status}` : "")
                      ).join("\n")
                      + (qa.claims.length > 1 ? "\n— combined across crop years for this quarter —" : "")
                      + (allAuth
                          ? "\nQuarter from M13 claim detail (P28 → endorsement)."
                          : "\nQuarter inferred from check date (no P28 detail).");
                      return (
                        <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?80:110,cursor:"help"}} title={tip}>
                          <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#1a6a3a",textTransform:"uppercase"}}>
                            ✓ Actual Paid{allAuth ? "" : " ~"}
                          </span>
                          <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,fontSize:14,color:"#1a6a3a"}}>
                            {"$"+Math.round(qa.amount).toLocaleString()}
                          </span>
                          {showEst && estIndemnity != null && estIndemnity > 0 && (
                            <span style={{fontSize:9,color:"#9a8270",fontWeight:600,marginTop:1}}>
                              est. was ${Math.round(estIndemnity).toLocaleString()}
                            </span>
                          )}
                        </div>
                      );
                    })()}

                    {/* Ended — awaiting payment (admin): the unpaid crop-year cells and their estimates */}
                    {showEst && qs.status.state === "awaiting" && (
                      <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?100:150}}>
                        <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#7a4a00",textTransform:"uppercase"}}>
                          Ended — awaiting payment
                        </span>
                        {qs.status.awaiting.map(c => (
                          <span key={c.cropYear} style={{fontSize:11,color:"#7a4a00",fontWeight:700}}>
                            {c.est && c.est.amount != null ? (c.est.amount > 0 ? "~$"+Math.round(c.est.amount).toLocaleString() : "$0 so far") : "not priced yet"} on {c.policies.join(", ") || "CY"+c.cropYear}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Est. Indemnity chip — when no paid claim is matched to this quarter yet (admin only) */}
                    {showEst && (<>
                    {!actualByQuarter[q] && estQ && estQ.amount == null && (
                      <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?80:110}}
                        title={drpUnpricedReason(actuals)}>
                        <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#60735f",textTransform:"uppercase"}}>
                          Est. Indemnity
                        </span>
                        <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:700,fontSize:14,color:"#60735f"}}>
                          Not priced yet
                        </span>
                        <span style={{fontSize:9,color:"#60735f",fontWeight:600,marginTop:1,maxWidth:180}}>
                          {drpUnpricedReason(actuals)}
                        </span>
                      </div>
                    )}
                    {!actualByQuarter[q] && estIndemnity != null && (
                      <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?80:110}}>
                        <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",
                          color: estIndemnity > 0 ? "#7a4a00" : "#4a7a4a",
                          textTransform:"uppercase"}}>
                          Est. Indemnity{actuals?.hasEstimates?" ⚡":actuals?.isPartial?" *":""}{yafAvg!=null?" Y":""}
                        </span>
                        <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:700,fontSize:14,
                          color: estIndemnity > 0 ? "#b85000" : "#2d5a2d"}}>
                          {estIndemnity > 0
                            ? "$"+Math.round(estIndemnity).toLocaleString()
                            : "—  No trigger"}
                        </span>
                        {estQ?.partial && (
                          <span style={{fontSize:9,color:"#7a4a00",fontWeight:600,marginTop:1}}
                            title={drpUnpricedReason(actuals)}>
                            {estQ.priced} of {estQ.priced + estQ.unpriced} endorsements priced
                          </span>
                        )}
                        {actuals?.rmaEstimated && (
                          <span style={{fontSize:9,color:"#b85000",fontWeight:600,marginTop:1}}>
                            includes RMA est.
                          </span>
                        )}
                        {actuals?.silasEstimated && (
                          <span style={{fontSize:9,color:"#b85000",fontWeight:600,marginTop:1,maxWidth:220}}>
                            {estNote(estIndemnity, rows)}
                          </span>
                        )}
                        {yafAvg != null && (
                          <span title={`RMA Yield Adjustment Factor: actual yield ÷ expected yield for each endorsement's RMA milk yield ID (ADM A00832), rounded to 4 places. Applied to ${yieldSum.finalN} of ${yieldSum.total} endorsements.`}
                            style={{fontSize:9,color: yafAvg < 1 ? "#2d5a2d" : "#7a4a00",fontWeight:700,marginTop:1,cursor:"help"}}>
                            YAF {yafAvg.toFixed(4)} (RMA)
                          </span>
                        )}
                        {yieldSum.pendingText && (
                          <span style={{fontSize:9,color:"#60735f",fontWeight:600,marginTop:1,maxWidth:220}}>
                            {yieldSum.pendingText}
                          </span>
                        )}
                        {yieldSum.unknownText && (
                          <span title={yieldSum.reasons.join("; ")} style={{fontSize:9,color:"#a33",fontWeight:700,marginTop:1,maxWidth:220,cursor:"help"}}>
                            {yieldSum.unknownText}
                          </span>
                        )}
                      </div>
                    )}
                    </>)}

                    {/* Est. Production / % Insured */}
                    {(() => {
                      const perQ = drpEstProd.__perQuarter === true;
                      const perQVal = drpEstProd[q] || '';
                      const globalVal = drpEstProd.__global || '';
                      const activeVal = perQ ? perQVal : globalVal;
                      const estNum = parseFloat((activeVal+'').replace(/,/g,''));
                      const pctInsured = estNum > 0 && totlbs > 0 ? Math.min(totlbs / estNum * 100, 100) : null;
                      function saveEstProd(next) {
                        setDrpEstProd(next);
                        if (farmer?.id) {
                          const mergedMeta = { ...(farmer.metadata || {}), drpEstProd: next };
                          fetch(`${SUPABASE_URL}/rest/v1/farmers?id=eq.${farmer.id}`, {
                            method: "PATCH",
                            headers: { ...getAuthHeaders(), Prefer: "return=minimal" },
                            body: JSON.stringify({ metadata: mergedMeta })
                          }).catch(() => {});
                        }
                      }
                      return (
                        <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}
                          onClick={ev => ev.stopPropagation()}>
                          {perQ && (
                            <div style={{display:"flex",flexDirection:"column"}}>
                              <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#60735f",textTransform:"uppercase",marginBottom:3,whiteSpace:"nowrap"}}>Est. Production</span>
                              <input
                                type="text"
                                placeholder="lbs"
                                value={perQVal}
                                onChange={ev => saveEstProd({...drpEstProd, [q]: ev.target.value})}
                                style={{width:isMobile?90:110,padding:"4px 8px",fontFamily:"'Source Sans 3',sans-serif",fontSize:12,
                                  textAlign:"right",border:"1px solid #c8d8c8",borderRadius:5,
                                  background:"#f4f8f4",color:"#1a2e1a",outline:"none"}}
                              />
                            </div>
                          )}
                          {pctInsured != null && (
                            <div style={{display:"flex",flexDirection:"column",minWidth:isMobile?100:140}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:3}}>
                                <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:"#60735f",textTransform:"uppercase"}}>% Insured</span>
                                <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:700,fontSize:12,color:"#1a2e1a"}}>{(totlbs/estNum*100).toFixed(1)}%</span>
                              </div>
                              <div style={{height:8,borderRadius:4,background:"#e4ece4",overflow:"hidden",width:isMobile?100:140,display:"flex"}}>
                                <div style={{
                                  height:"100%",
                                  width:Math.min(IIIlbs/estNum*100,100)+"%",
                                  background:"#1a4a7a",
                                  transition:"width 0.3s ease",
                                  flexShrink:0
                                }}/>
                                <div style={{
                                  height:"100%",
                                  width:Math.min(IVlbs/estNum*100,100)+"%",
                                  background:"#4a8a4a",
                                  transition:"width 0.3s ease",
                                  flexShrink:0
                                }}/>
                              </div>
                              <div style={{display:"flex",gap:8,marginTop:3}}>
                                {IIIlbs > 0 && <span style={{fontSize:10,color:"#1a4a7a",fontWeight:700}}>III {(IIIlbs/estNum*100).toFixed(1)}%</span>}
                                {IVlbs > 0  && <span style={{fontSize:10,color:"#4a8a4a",fontWeight:700}}>IV {(IVlbs/estNum*100).toFixed(1)}%</span>}
                              </div>
                              {(() => { const lim = drpCoveredMilkLimit(totlbs, estNum); return lim && lim.text && (
                                <span title={lim.basis} style={{fontSize:9.5,marginTop:2,maxWidth:200,cursor:"help",fontWeight:600,
                                  color: lim.within ? "#4a6a4a" : "#a33"}}>{lim.text}</span>); })()}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Expand caret */}
                    <span style={{marginLeft:"auto",fontSize:14,color:"#aaa",
                      transition:"transform 0.15s",display:"inline-block",
                      transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
                  </div>
                  {/* Distance to trigger, per class (admin only) */}
                  {distLines.length > 0 && (
                    <div style={{padding:isMobile?"0 12px 10px":"0 20px 12px",fontSize:12,color:"#4a5a4a",fontWeight:600,display:"flex",flexDirection:"column",gap:2}}>
                      {distLines.map((t, i) => <span key={i}>{t}{i === distLines.length - 1 ? yafNote : ""}</span>)}
                    </div>
                  )}

                  {/* Expanded detail — Class III then Class IV breakdown */}
                  {isOpen && (
                    <div style={{borderTop:"1px solid #e4ece4",padding:isMobile?"12px 14px":"16px 20px",
                      display:"flex",flexDirection:"column",gap:16}}>

                      {/* ── MONTH-BY-MONTH PRICE BREAKDOWN ── */}
                      {monthDetail.length > 0 && (
                        <div style={{background:"#fafbfc",borderRadius:7,padding:"12px 14px",
                          border:"1px solid #d8e0e8"}}>
                          <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",
                            color:"#5a6a7a",marginBottom:10,textTransform:"uppercase",
                            display:"flex",alignItems:"center",gap:6}}>
                            📅 Monthly Price Breakdown
                          </div>
                          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:380}}>
                              <thead>
                                <tr style={{borderBottom:"2px solid #d8e0e8"}}>
                                  <th style={{textAlign:"left",padding:"6px 10px",fontSize:10,fontWeight:800,
                                    color:"#5a6a7a",textTransform:"uppercase",letterSpacing:"0.08em"}}>Month</th>
                                  {III.length > 0 && <th style={{textAlign:"right",padding:"6px 10px",fontSize:10,fontWeight:800,
                                    color:"#1a4a7a",textTransform:"uppercase",letterSpacing:"0.08em"}}>Class III</th>}
                                  {IV.length > 0 && <th style={{textAlign:"right",padding:"6px 10px",fontSize:10,fontWeight:800,
                                    color:"#2d5a2d",textTransform:"uppercase",letterSpacing:"0.08em"}}>Class IV</th>}
                                  <th style={{textAlign:"center",padding:"6px 10px",fontSize:10,fontWeight:800,
                                    color:"#5a6a7a",textTransform:"uppercase",letterSpacing:"0.08em"}}>Source</th>
                                </tr>
                              </thead>
                              <tbody>
                                {monthDetail.map((mo,mi) => {
                                  const srcIII = mo.sourceIII;
                                  const srcIV  = mo.sourceIV;
                                  // Pick the most notable source for the row badge
                                  const primarySrc = (III.length > 0 ? srcIII : srcIV);
                                  const srcColor = primarySrc === "announced" ? "#2d6a1a" : primarySrc === "estimated" ? "#b85000" : "#999";
                                  const srcBg    = primarySrc === "announced" ? "#e6f4e0" : primarySrc === "estimated" ? "#fff4e6" : "#f0f0f0";
                                  const primaryKind = III.length > 0 ? mo.estKindIII : mo.estKindIV;
                                  const srcLabel = primarySrc === "announced" ? "USDA ✓" : primarySrc === "estimated"
                                    ? (primaryKind === "silas" ? (mo.futuresBased ? "Market est. · RMA futures" : mo.carriedForward ? "Market est. · carried" : "Market est.") : "RMA Est.") : "Pending";
                                  return (
                                    <tr key={mo.key} style={{borderBottom:mi<2?"1px solid #e8ecf0":"none"}}>
                                      <td style={{padding:"8px 10px",fontWeight:700,color:"#1a2e1a"}}>{mo.label}</td>
                                      {III.length > 0 && (
                                        <td style={{padding:"8px 10px",textAlign:"right",fontFamily:"'Source Sans 3',sans-serif",fontWeight:700,
                                          color: srcIII==="announced"?"#1a2e1a":srcIII==="estimated"?"#b85000":"#bbb"}}>
                                          {mo.classIII != null ? "$"+mo.classIII.toFixed(2) : "—"}
                                        </td>
                                      )}
                                      {IV.length > 0 && (
                                        <td style={{padding:"8px 10px",textAlign:"right",fontFamily:"'Source Sans 3',sans-serif",fontWeight:700,
                                          color: srcIV==="announced"?"#1a2e1a":srcIV==="estimated"?"#b85000":"#bbb"}}>
                                          {mo.classIV != null ? "$"+mo.classIV.toFixed(2) : "—"}
                                        </td>
                                      )}
                                      <td style={{padding:"8px 10px",textAlign:"center"}}>
                                        <span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:10,
                                          background:srcBg,color:srcColor,letterSpacing:"0.04em"}}>
                                          {srcLabel}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                                {/* Quarter average row */}
                                <tr style={{borderTop:"2px solid #c8d0d8",background:"#f0f3f7"}}>
                                  <td style={{padding:"8px 10px",fontWeight:800,fontSize:11,color:"#1a2e1a",
                                    textTransform:"uppercase",letterSpacing:"0.06em"}}>Qtr Average</td>
                                  {III.length > 0 && (
                                    <td style={{padding:"8px 10px",textAlign:"right",fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,
                                      fontSize:13,color:"#1a4a7a"}}>
                                      {actuals?.classIII != null ? "$"+actuals.classIII.toFixed(2) : "—"}
                                    </td>
                                  )}
                                  {IV.length > 0 && (
                                    <td style={{padding:"8px 10px",textAlign:"right",fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,
                                      fontSize:13,color:"#2d5a2d"}}>
                                      {actuals?.classIV != null ? "$"+actuals.classIV.toFixed(2) : "—"}
                                    </td>
                                  )}
                                  <td style={{padding:"8px 10px",textAlign:"center"}}>
                                    {actuals?.hasEstimates && (
                                      <span style={{fontSize:9,fontWeight:700,color:"#b85000"}}>
                                        includes estimates
                                      </span>
                                    )}
                                    {actuals && !actuals.hasEstimates && !actuals.isPartial && (
                                      <span style={{fontSize:9,fontWeight:700,color:"#2d6a1a"}}>
                                        all announced
                                      </span>
                                    )}
                                    {actuals?.isPartial && !actuals?.hasEstimates && (
                                      <span style={{fontSize:9,fontWeight:700,color:"#999"}}>
                                        {(actuals.monthsKnownIII||0)}/3 months
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* CLASS III block */}
                      {III.length > 0 && (
                        <React.Fragment>
                          <div style={{background:"#f0f5fc",borderRadius:7,padding:"14px 16px",
                            border:"1px solid #1a4a7a22"}}>
                            <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",
                              color:"#1a4a7a",marginBottom:10,textTransform:"uppercase"}}>
                              Class III — {III.length} endorsement{III.length>1?"s":""} · {drpFmtM(IIIlbs)} lbs
                            </div>
                            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10}}>
                              {[
                                {lbl:"Wtd Avg Coverage",     val: IIIavgCov  != null ? (IIIavgCov*100).toFixed(0)+"%" : "—"},
                                {lbl:"Wtd Avg Full Price",    val: IIIavgFull != null ? "$"+IIIavgFull.toFixed(2)+"/cwt" : "—"},
                                {lbl:"Wtd Avg Trigger Price", val: IIIavgTrig != null ? "$"+IIIavgTrig.toFixed(2)+"/cwt" : "—"},
                                {lbl:"Wtd Avg Premium",       val: IIIavgPrem != null ? "$"+IIIavgPrem.toFixed(4)+"/cwt" : "—"},
                                {lbl:"Est. Net Floor",        val: IIInetFloor != null ? "$"+IIInetFloor.toFixed(2)+"/cwt" : "—",
                                 highlight: true},
                              ].map(item=>(
                                <div key={item.lbl} style={{background:item.highlight?"#1a4a7a":"#fff",
                                  borderRadius:5,padding:"8px 12px",
                                  border:"1px solid "+(item.highlight?"#1a4a7a":"#1a4a7a22")}}>
                                  <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.08em",
                                    color:item.highlight?"#b8d4f8":"#5a7aaa",textTransform:"uppercase",marginBottom:4}}>
                                    {item.lbl}
                                  </div>
                                  <div style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,
                                    fontSize:item.highlight?15:13,
                                    color:item.highlight?"#fff":"#1a4a7a"}}>
                                    {item.val}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          {/* Actual price + est indemnity row for Class III */}
                          {actuals && !actuals.isFuture && actuals.classIII != null && (
                            <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",
                              padding:"8px 12px",background: actuals.hasEstimates ? "#fef7ee" : "#e0eafa",borderRadius:5,
                              border:"1px solid "+(actuals.hasEstimates ? "#e8b85033" : "#1a4a7a33")}}>
                              <div>
                                <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color: actuals.hasEstimates ? "#b85000" : "#1a4a7a",textTransform:"uppercase",marginRight:6}}>
                                  {actuals.isFullyEstimated ? (actuals.estWord==="RMA"?"RMA":"Market")+" Est. Qtr Avg" : actuals.hasEstimates ? "Blended Qtr Avg ("+actuals.monthsKnownIII+" USDA + "+actuals.monthsEstIII+" "+actuals.estWord+")" : "USDA Actual Qtr Avg"}{actuals.isPartial && !actuals.hasEstimates ? " ("+actuals.monthsKnownIII+"/3 mo)" : ""}:
                                </span>
                                <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,fontSize:14,color:"#1a2e1a"}}>
                                  {"$"+actuals.classIII.toFixed(2)+"/cwt"}
                                </span>
                              </div>
                              {showEst && estIIIIndem != null && (
                                <div style={{marginLeft:8}}>
                                  <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",
                                    color: estIIIIndem > 0 ? "#7a4a00":"#2d5a2d",
                                    textTransform:"uppercase",marginRight:6}}>
                                    Est. Indemnity{actuals.isPartial || actuals.hasEstimates?" *":""}:
                                  </span>
                                  <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,fontSize:15,
                                    color: estIIIIndem > 0 ? "#b85000":"#2d5a2d"}}>
                                    {estIIIIndem > 0 ? "$"+Math.round(estIIIIndem).toLocaleString() : "No trigger — $0"}
                                  </span>
                                  {actuals.silasEstimated && (
                                    <span style={{display:"block",fontSize:9,color:"#b85000",fontWeight:600,marginTop:2}}>{estNote(estIIIIndem, III)}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      )}
                      {/* CLASS IV block */}
                      {IV.length > 0 && (
                        <React.Fragment>
                          <div style={{background:"#f0f5f0",borderRadius:7,padding:"14px 16px",
                            border:"1px solid #2d5a2d22"}}>
                            <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.12em",
                              color:"#2d5a2d",marginBottom:10,textTransform:"uppercase"}}>
                              Class IV — {IV.length} endorsement{IV.length>1?"s":""} · {drpFmtM(IVlbs)} lbs
                            </div>
                            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10}}>
                              {[
                                {lbl:"Wtd Avg Coverage",     val: IVavgCov  != null ? (IVavgCov*100).toFixed(0)+"%" : "—"},
                                {lbl:"Wtd Avg Full Price",    val: IVavgFull != null ? "$"+IVavgFull.toFixed(2)+"/cwt" : "—"},
                                {lbl:"Wtd Avg Trigger Price", val: IVavgTrig != null ? "$"+IVavgTrig.toFixed(2)+"/cwt" : "—"},
                                {lbl:"Wtd Avg Premium",       val: IVavgPrem != null ? "$"+IVavgPrem.toFixed(4)+"/cwt" : "—"},
                                {lbl:"Est. Net Floor",        val: IVnetFloor != null ? "$"+IVnetFloor.toFixed(2)+"/cwt" : "—",
                                 highlight: true},
                              ].map(item=>(
                                <div key={item.lbl} style={{background:item.highlight?"#2d5a2d":"#fff",
                                  borderRadius:5,padding:"8px 12px",
                                  border:"1px solid "+(item.highlight?"#2d5a2d":"#2d5a2d22")}}>
                                  <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.08em",
                                    color:item.highlight?"#b8d8b8":"#4a7a4a",textTransform:"uppercase",marginBottom:4}}>
                                    {item.lbl}
                                  </div>
                                  <div style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,
                                    fontSize:item.highlight?15:13,
                                    color:item.highlight?"#fff":"#2d5a2d"}}>
                                    {item.val}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          {/* Actual price + est indemnity row for Class IV */}
                          {actuals && !actuals.isFuture && actuals.classIV != null && (
                            <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",
                              padding:"8px 12px",background: actuals.hasEstimates ? "#fef7ee" : "#e0f0e0",borderRadius:5,
                              border:"1px solid "+(actuals.hasEstimates ? "#e8b85033" : "#2d5a2d33")}}>
                              <div>
                                <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color: actuals.hasEstimates ? "#b85000" : "#2d5a2d",textTransform:"uppercase",marginRight:6}}>
                                  {actuals.isFullyEstimated ? (actuals.estWord==="RMA"?"RMA":"Market")+" Est. Qtr Avg" : actuals.hasEstimates ? "Blended Qtr Avg ("+actuals.monthsKnownIV+" USDA + "+actuals.monthsEstIV+" "+actuals.estWord+")" : "USDA Actual Qtr Avg"}{actuals.isPartial && !actuals.hasEstimates ? " ("+actuals.monthsKnownIV+"/3 mo)" : ""}:
                                </span>
                                <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,fontSize:14,color:"#1a2e1a"}}>
                                  {"$"+actuals.classIV.toFixed(2)+"/cwt"}
                                </span>
                              </div>
                              {showEst && estIVIndem != null && (
                                <div style={{marginLeft:8}}>
                                  <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",
                                    color: estIVIndem > 0 ? "#7a4a00":"#2d5a2d",
                                    textTransform:"uppercase",marginRight:6}}>
                                    Est. Indemnity{actuals.isPartial || actuals.hasEstimates?" *":""}:
                                  </span>
                                  <span style={{fontFamily:"'Source Sans 3',sans-serif",fontWeight:800,fontSize:15,
                                    color: estIVIndem > 0 ? "#b85000":"#2d5a2d"}}>
                                    {estIVIndem > 0 ? "$"+Math.round(estIVIndem).toLocaleString() : "No trigger — $0"}
                                  </span>
                                  {actuals.silasEstimated && (
                                    <span style={{display:"block",fontSize:9,color:"#b85000",fontWeight:600,marginTop:2}}>{estNote(estIVIndem, IV)}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* USDA prices + how the estimate is built — two short lines, detail behind a disclosure.
              The Class prices load themselves from class_prices (ams-sync, timed to AMS's own
              release schedule), so there is no manual "check USDA" step any more. */}
          <div style={{marginTop:14,padding:"10px 16px",background:"#f4f8f4",border:"1px solid #cfe0cf",
            borderRadius:7,fontSize:12,color:"#2d4a2d",lineHeight:1.5}}>
            <div><strong>📡 {nextUsdaText}</strong>{cmeFutures?.updated && <span style={{color:"#60735f"}}> · months not yet announced use RMA's futures-implied prices (RMA {cmeFutures.updated})</span>}</div>
            {showEst && (
              <>
                <div style={{color:"#7a6a00"}}>Estimates are not official — the insurance company calculates the actual payment. ✓ Actual Paid is the processed claim.</div>
                <details style={{marginTop:4}}>
                  <summary style={{cursor:"pointer",fontWeight:700,fontSize:11.5}}>How this is estimated</summary>
                  <div style={{fontSize:11,color:"#60735f",marginTop:6}}>
                    <strong>✓ Actual Paid</strong> is the real indemnity from the insurer's processed claim, attributed to the quarter it settled.
                    <strong> Est. Indemnity</strong> averages USDA AMS announced Class III and Class IV monthly prices over the quarter.
                    Months not yet announced use RMA's daily futures-implied expected prices (the prices RMA uses to set DRP).
                    Once a quarter's sales close and RMA stops publishing, those months use a daily market estimate: a month in
                    progress from USDA's own Class III/IV formula and the latest dairy product prices; a month not yet started from
                    RMA's last futures price for it, moved by what RMA's next month has moved since. Each endorsement carries RMA's
                    own yield adjustment (ADM A00832) once RMA publishes it. * = partial quarter · ⚡ = includes RMA futures-implied
                    estimates. Sources: <em>USDA AMS Announcement of Class and Component Prices</em>, <em>RMA Actuarial Data Master (Plan 83)</em>.
                  </div>
                </details>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── ALL ENDORSEMENTS TAB ── */}
      {tab==="all" && (() => {
        const inDateRange = (e) => {
          const d = toISO(e.effectiveDate);
          if (filterFrom && (!d || d < filterFrom)) return false;
          if (filterTo   && (!d || d > filterTo))   return false;
          return true;
        };
        const matchAll = (e) => (filterQ==="all" || e.quarter===filterQ) && inDateRange(e);
        let filtered = allEnds.filter(matchAll);
        if (sortCol) {
          const dir = sortDir === "asc" ? 1 : -1;
          filtered = [...filtered].sort((a, b) => {
            if (sortCol === "date") {
              const va = toISO(a.effectiveDate) || "", vb = toISO(b.effectiveDate) || "";
              return va < vb ? -dir : va > vb ? dir : 0;
            }
            if (sortCol === "quarter") {
              return (drpSortKey(a.quarter) - drpSortKey(b.quarter)) * dir;
            }
            return 0;
          });
        }
        const arrow = (col) => sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅";
        return (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:16,flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:12,color:"#60735f",fontWeight:600}}>Quarter:</span>
              <select value={filterQ} onChange={e=>setFilterQ(e.target.value)}
                style={{padding:"5px 12px",borderRadius:6,border:"1px solid #c8d8c8",
                  fontSize:12,color:"#1a2e1a",background:"#fff",cursor:"pointer",outline:"none"}}>
                <option value="all">All Quarters</option>
                {quarters.map(q=><option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
              <span style={{fontSize:12,color:"#60735f",fontWeight:600}}>Purchased:</span>
              <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)}
                style={{padding:"4px 8px",borderRadius:6,border:"1px solid #c8d8c8",fontSize:12,color:"#1a2e1a",background:"#fff",outline:"none"}} />
              <span style={{fontSize:12,color:"#60735f"}}>to</span>
              <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)}
                style={{padding:"4px 8px",borderRadius:6,border:"1px solid #c8d8c8",fontSize:12,color:"#1a2e1a",background:"#fff",outline:"none"}} />
              {(filterFrom||filterTo) && (
                <button onClick={()=>{setFilterFrom("");setFilterTo("");}}
                  style={{padding:"4px 9px",borderRadius:6,border:"1px solid #c8d8c8",fontSize:11,color:"#60735f",background:"#fff",cursor:"pointer"}}>clear</button>
              )}
            </div>
            <span style={{marginLeft:"auto",fontSize:12,color:"#60735f"}}>
              {filtered.length} endorsements
            </span>
          </div>

          <div style={{background:"#fff",border:"1px solid #e4ece4",borderRadius:8,overflow:"hidden"}}>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:"#f9fbf9"}}>
                    {[
                      {h:"END #",        left:true},
                      {h:"PURCHASE DATE",left:true, sort:"date"},
                      {h:"QUARTER",      left:true, sort:"quarter"},
                      {h:"CLASS",        left:true},
                      {h:"MILK (LBS)",   left:false},
                      {h:"MILK/COW",     left:false},
                      {h:"FULL PRICE",   left:false},
                      {h:"TRIGGER $",    left:false},
                      {h:"PF",           left:false},
                      {h:"LIABILITY",    left:false},
                      {h:"PROD PREM",    left:false},
                    ].map(col=>{
                      const sortable = !!col.sort;
                      const active = sortable && sortCol === col.sort;
                      return (
                      <th key={col.h}
                        onClick={sortable ? () => toggleSort(col.sort) : undefined}
                        title={sortable ? "Click to sort" : undefined}
                        style={{padding:"10px 14px",textAlign:col.left?"left":"right",
                        fontSize:10,fontWeight:800,letterSpacing:"0.1em",
                        color: active ? "#2d4a2d" : "#4a7a4a",borderBottom:"2px solid #e4ece4",whiteSpace:"nowrap",
                        cursor: sortable ? "pointer" : "default", userSelect:"none"}}>
                        {col.h}{sortable && <span style={{color: active ? "#2d4a2d" : "#bcd0bc",fontSize:10}}>{arrow(col.sort)}</span>}
                      </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtered
                    .map((e,i)=>{
                      const isIII = e.classIII===1||e.classIII===true;
                      const qc = drpQColor(e.quarter);
                      const pf = drpGetProtectionFactor(e);
                      const fmtPrice = (v) => v!=null ? "$"+Number(v).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2}) : "—";
                      return (
                        <tr key={i} style={{borderBottom:"1px solid #f0f4f0",background:i%2===0?"#fff":"#fafcfa"}}>
                          <td style={{padding:"11px 14px",fontWeight:700,color:"#1a2e1a"}}>{e.endNum}</td>
                          <td style={{padding:"11px 14px",fontVariantNumeric: "tabular-nums",fontSize:12,color:"#555",whiteSpace:"nowrap"}}>
                            {e.effectiveDate || "—"}
                          </td>
                          <td style={{padding:"11px 14px"}}>
                            <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:5,
                              background:qc.bg,color:qc.color,border:"1px solid "+qc.color+"33"}}>
                              {e.quarter}
                            </span>
                          </td>
                          <td style={{padding:"11px 14px"}}>
                            <span style={{fontSize:11,fontWeight:800,padding:"2px 9px",borderRadius:4,
                              background:isIII?"#e8f0fa":"#eaf0e8",
                              color:isIII?"#1a4a7a":"#2d5a2d",
                              border:isIII?"1px solid #1a4a7a44":"1px solid #2d5a2d44"}}>
                              {isIII?"III":"IV"}
                            </span>
                          </td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:700}}>
                            {Number(e.declaredMilkProd||0).toLocaleString()}
                          </td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",color:"#555"}}>
                            {(() => { const v = mpcOf(e); return v ? Number(v).toLocaleString() : "—"; })()}
                          </td>
                          <td style={{padding:"11px 14px",textAlign:"right"}}>
                            <span style={{fontVariantNumeric: "tabular-nums",fontWeight:700,color:"#555",fontSize:13}}>
                              {fmtPrice(e.projectedPrice)}
                            </span>
                          </td>
                          <td style={{padding:"11px 14px",textAlign:"right"}}>
                            <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,color:"#1a6a3a",fontSize:14}}>
                              {drpPriceCwtText(e.triggerPrice)}
                            </span>
                          </td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontSize:12}}>
                            {pf > 1.0
                              ? <span style={{fontWeight:800,color:"#7a4a00",background:"#fff8e8",padding:"2px 7px",borderRadius:4,border:"1px solid #c8860a44"}}>{pf.toFixed(2)}</span>
                              : <span style={{color:"#aaa"}}>1.00</span>}
                          </td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",color:"#555"}}>
                            {"$"+Number(e.liability||0).toLocaleString()}
                          </td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontVariantNumeric: "tabular-nums",fontWeight:800,color:"#1a2e1a"}}>
                            {"$"+Number(e.producerPremium||0).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── CLAIMS HISTORY TAB ── */}
      {/* DRP indemnities sourced from M13 P20/P20A tagged policy_type='drp'.
          Cause-of-loss column is intentionally suppressed for non-MPCI
          products; DRP losses are revenue-based, not weather-cause. */}
      {tab==="claims" && (
        <FarmerClaimsHistory farmer={farmer} productFilter="drp" />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APH STATUS BANNER
// ─────────────────────────────────────────────────────────────────────────────
function APHBanner({ farmer, fallOnly }) {
  // Collapsed by default — the APH status is a useful at-a-glance indicator for
  // us, but it isn't the headline of the card, so it stays a slim one-liner you
  // can expand to see the per-crop grid.
  const [aphOpen, setAphOpen] = useState(false);
  const approvedYields = farmer.approvedYields || [];
  const ppEligibility  = dedupPPRows(farmer.ppEligibility  || []);

  const allCropRows = fallOnly
    ? approvedYields.filter(a => FALL_CROPS.includes(a.crop))
    : [
        ...[
          // Process approvedYields FIRST (M13 data with specific types like GSG/NTS)
          ...approvedYields
            .filter(a => !FALL_CROPS.includes(a.crop))
            .map(a => ({ county: a.county, crop: a.crop, type: a.type||"", practice: normalizePractice(a.practice) })),
          // Then PP eligibility — only adds entries not already covered by approvedYields.
          // Carry the source pp row (_pp) so a PP-eligible crop the farmer isn't
          // insuring THIS year (no crop_coverage APH, no current PP liability —
          // e.g. Kirby's dry beans, grown 2023–24, 0 ac 2025–26) can render as a
          // neutral "PP eligible · last grown YYYY" chip instead of a false
          // "missing approved yield" alarm. Its historical APH lives in APH History.
          ...ppEligibility
            .filter(p => !FALL_CROPS.includes(p.crop))
            .map(p => ({ county: p.county, crop: p.crop, type: p.type||"", practice: normalizePractice(p.practice), _fromPP: true, _pp: p })),
        ].reduce((acc, r) => {
          // When a PP entry is generic (empty type/practice OR type='ALL' /
          // practice='No Practice'), skip it if any approvedYield entry already
          // covers the same county+crop. Otherwise we get a phantom "POTATOES ·
          // ALL · No Practice · missing" pill alongside the real "POTATOES ·
          // TBL" row, even though the APH data is on file.
          if (r._fromPP && isGenericPPRow(r.type, r.practice)) {
            const hasMatch = acc.some(x =>
              (x.county||"").toUpperCase().trim() === (r.county||"").toUpperCase().trim() &&
              (x.crop||"").toUpperCase() === (r.crop||"").toUpperCase()
            );
            if (hasMatch) return acc;
          }
          if (!acc.some(x =>
            (x.county||"").toUpperCase().trim() === (r.county||"").toUpperCase().trim() &&
            (x.crop||"").toUpperCase() === (r.crop||"").toUpperCase() &&
            normalizeCropType(x.type) === normalizeCropType(r.type) &&
            x.practice === r.practice
          )) acc.push(r);
          return acc;
        }, []),
      ];

  if (allCropRows.length === 0) return null;

  const rows = allCropRows.map(r => {
    const ay = approvedYields.find(a =>
      (a.county||"").toUpperCase().trim()===(r.county||"").toUpperCase().trim() &&
      (a.crop||"").toUpperCase()===(r.crop||"").toUpperCase() &&
      normalizeCropType(a.type)===normalizeCropType(r.type) &&
      (a.practice||"").toUpperCase()===(r.practice||"").toUpperCase()
    );
    const ppRow = ppEligibility.find(p =>
      (p.county||"").toUpperCase().trim()===(r.county||"").toUpperCase().trim() &&
      (p.crop||"").toUpperCase()===(r.crop||"").toUpperCase() &&
      normalizeCropType(p.type)===normalizeCropType(r.type) &&
      normalizePractice(p.practice)===normalizePractice(r.practice)
    );
    const ppHasLiability = ppRow?.ppLiabilityPerAcre != null && ppRow.ppLiabilityPerAcre > 0;
    // PP-only rows carry parent-commodity types (SOYBEANS COMM/SSFG, POTATOES ALL)
    // that don't strict-match crop_coverage sub-types (DC/NTS/TBL). If there's any
    // approvedYield on the same county+crop, the PP guarantee is computable —
    // surface that yield (with practice-match preference) so the pill renders
    // with real data instead of "missing approved yield".
    const ayResolved = ay || (r._fromPP ? (() => {
      const candidates = approvedYields.filter(a =>
        (a.county||"").toUpperCase().trim()===(r.county||"").toUpperCase().trim() &&
        (a.crop||"").toUpperCase()===(r.crop||"").toUpperCase() &&
        a.approvedYield != null && a.approvedYield > 0
      );
      if (candidates.length === 0) return null;
      return candidates.find(a => normalizePractice(a.practice) === normalizePractice(r.practice))
          || candidates.find(a => !(a.practice||"") && !(r.practice||""))
          || candidates[0];
    })() : null);
    const isSilage = normalizeCropType(r.type) === "026";
    const hasAY = !!(ayResolved && ayResolved.approvedYield != null && ayResolved.approvedYield > 0);
    // A PP-eligible crop the farmer isn't INSURING this year: it surfaced only
    // from pp_eligibility (no crop_coverage approved yield) and carries no current
    // PP liability. It's eligible with acreage history — not a missing-APH error.
    // (e.g. Kirby's dry beans: grown 2023–24, 0 ac 2025–26, APH still in APH History.)
    const eligibleOnly = !!r._fromPP && !hasAY && !ppHasLiability;
    return {
      county:   r.county,
      crop:     r.crop,
      type:     r.type||"",
      practice: r.practice||"",
      isSilage,
      ay: ayResolved,
      ppRow,
      ppHasLiability,
      eligibleOnly,
      lastGrown: eligibleOnly ? ppLastGrown(r._pp) : null,
      complete: hasAY || ppHasLiability,
    };
  })
  // Only surface corn SILAGE (GSS/026) when a real silage APH is on file — never a
  // stray PP-eligibility shell (see silageHasApprovedAph).
  .filter(r => silageHasApprovedAph(r.isSilage, r.ppRow?.aphYield, r.ay));

  // Crops the farmer is INSURING this year (crop_coverage APH or current PP
  // liability) gate completeness. PP-eligible-but-not-planted crops are
  // informational — they never trip the "APH incomplete" warning.
  const insuredRows  = rows.filter(r => !r.eligibleOnly);
  const eligibleRows = rows.filter(r =>  r.eligibleOnly);
  const complete = insuredRows.filter(r => r.complete);
  const missing  = insuredRows.filter(r => !r.complete);
  const allGood  = missing.length === 0;
  // Distinguish "real APH on file" (approved yields written to crop_coverage)
  // from "PP-only" (we have ppLiabilityPerAcre from a PP report but never got an
  // APH document, so r.ay is null). Both satisfy `complete`, but the banner and
  // the per-crop chips should reflect which one it actually is.
  const allHaveAY = insuredRows.length > 0 && insuredRows.every(r => r.ay && r.ay.approvedYield != null && r.ay.approvedYield > 0);
  const allOnlyPP = insuredRows.length > 0 && !allHaveAY && allGood && insuredRows.every(r => r.ppHasLiability);

  // ── Acreage not yet reported ──────────────────────────────────────────────
  // The AIP has keyed the APH database but not the acreage report: an approved
  // yield with no acres and no liability behind it. That is a real state of the
  // file, not a defect, and it is what the card used to render as a full table
  // of "—" under a green "all crops have approved yields · 3/3 complete"
  // headline — which reads as "this farm has nothing", the one thing it does
  // not mean. Aaron Marquart billed $244,107 in CY2025 and his CY2026 card said
  // nothing at all.
  //
  // Read out of the SOURCE on 2026-08-13, not inferred: NAU's own
  // 953616-2026.dat (modified that morning) carries ZERO keyed P11 acreage
  // lines for 71 insureds — Aaron's policy 4005427 has 0 P11 rows against 3 P14
  // coverages and 37 P15 APH databases. Nothing on our side dropped it, and it
  // resolves itself the day the AIP keys the acreage report.
  const pendingAcreage = insuredRows.filter(r => r.ay && r.ay.acres == null && r.ay.liability == null);
  const allPending = insuredRows.length > 0 && pendingAcreage.length === insuredRows.length;

  // Group by county for display
  const counties = [...new Set(rows.map(r => r.county))];

  return (
    <div style={{
      marginBottom: 16,
      border: `1px solid ${allGood ? "#4a7a2a2e" : "#c8860a55"}`,
      borderRadius: 9,
      overflow: "hidden",
      background: allGood ? "#fafcf8" : "#fffbf2",
    }}>
      {/* Header bar — slim, clickable to expand the per-crop grid */}
      <div onClick={() => setAphOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 9, cursor: "pointer",
        padding: "7px 14px",
        background: allGood ? "#f3f8ef" : "#fef4e0",
        borderBottom: aphOpen ? `1px solid ${allGood ? "#4a7a2a22" : "#c8860a33"}` : "none",
      }}>
        <span style={{ fontSize: 12, color: "#9ab", width: 9 }}>{aphOpen ? "▾" : "▸"}</span>
        <span style={{ fontSize: 13 }}>{allPending ? "⏳" : allGood ? "✅" : "⚠️"}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: 11.5, color: allGood ? "#5a7a4a" : "#7a4a00" }}>
            {insuredRows.length === 0
              ? ("PP eligible — " + eligibleRows.length + " crop" + (eligibleRows.length !== 1 ? "s" : "") + " on file")
              : allPending
                ? ("Acreage not yet reported — " + insuredRows.length + " crop" + (insuredRows.length !== 1 ? "s" : "") +
                   " have approved yields, the AIP has not keyed acres")
              : allHaveAY
                ? ("APH on file — all " + complete.length + " crop" + (complete.length !== 1 ? "s" : "") + " have approved yields")
                : allOnlyPP
                  ? ("PP Liability on file — " + complete.length + " crop" + (complete.length !== 1 ? "s" : "") + " · APH document not yet uploaded")
                  : (insuredRows.length > 0 && insuredRows.every(r => r.ppHasLiability))
                    ? ("PP Liability on file — R&H PP worksheet loaded (" + complete.length + " crop" + (complete.length !== 1 ? "s" : "") + ")")
                    : ("APH incomplete — " + missing.length + " crop" + (missing.length !== 1 ? "s" : "") + " missing approved yield")}
            {eligibleRows.length > 0 && insuredRows.length > 0 && (
              <span style={{ fontWeight: 600, color: allGood ? "#5a7a3a" : "#a06020" }}> · {eligibleRows.length} PP-eligible</span>
            )}
            {/* Partly keyed — some counties reported, some not. Named per crop
                rather than folded into the headline, because the card DOES show
                real numbers for the keyed ones and a blanket "not reported"
                would contradict them. */}
            {!allPending && pendingAcreage.length > 0 && (
              <span style={{ fontWeight: 600, color: "#8a7320" }}> · {pendingAcreage.length} awaiting acreage</span>
            )}
          </span>
          {!allGood && (
            <span style={{ fontSize: 11, color: "#a06020", marginLeft: 8 }}>
              Missing approved yield data — check M13 sync status or contact AIP
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: "#aaa", fontVariantNumeric: "tabular-nums" }}>
          {insuredRows.length === 0
            ? (eligibleRows.length + " PP-eligible")
            : (complete.length + "/" + insuredRows.length + " complete")}
        </span>
      </div>

      {/* Crop grid — only when expanded */}
      {aphOpen && (
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {counties.map(county => {
          const countyRows = rows.filter(r => r.county === county);
          return (
            <div key={county}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: "#60735f", textTransform: "uppercase", marginBottom: 6 }}>
                {county} County
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {countyRows.map(r => {
                  // Look up SCO/ECO endorsements for this county+crop+type
                  const chipScoEco = (farmer.scoEcoEndorsements||[]).filter(e =>
                    (e.county||"").toUpperCase().trim() === (r.county||"").toUpperCase().trim() &&
                    (e.crop||"").toUpperCase() === (r.crop||"").toUpperCase() &&
                    (e.type||"").toUpperCase() === (r.type||"").toUpperCase()
                  );
                  const scoEnd = chipScoEco.find(e => (e.plan||"").toUpperCase().includes("SCO"));
                  const ecoEnd = chipScoEco.find(e => (e.plan||"").toUpperCase().includes("ECO"));
                  // Practice is suppressed when it's Non-Irrigated (the default), unless a
                  // sibling chip has the same crop+type with a different practice — in that
                  // case label both so Irrigated vs Non-Irrigated is explicit on the card.
                  const pracUp = (r.practice||"").toUpperCase();
                  const isDefaultPractice = !pracUp || pracUp === "NON-IRRIGATED" || pracUp === "NON IRR";
                  const hasSiblingDifferentPractice = countyRows.some(s =>
                    s !== r &&
                    (s.crop||"").toUpperCase() === (r.crop||"").toUpperCase() &&
                    (s.type||"").toUpperCase() === (r.type||"").toUpperCase() &&
                    (s.practice||"").toUpperCase() !== pracUp
                  );
                  const showPractice = r.practice && (!isDefaultPractice || hasSiblingDifferentPractice);
                  return (
                  <div key={`${r.crop}|${r.type}|${r.practice}`} style={{
                    display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap",
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: `1px solid ${r.complete ? (CROP_COLOR[r.crop]||"#4a7a2a")+"44" : r.eligibleOnly ? "#c4d0c4" : "#c8860a66"}`,
                    background: r.complete ? (CROP_BG[r.crop]||"#f4faf0") : r.eligibleOnly ? "#f3f6f3" : "#fff8ec",
                    fontSize: 12,
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                      background: r.complete ? (CROP_COLOR[r.crop]||"#4a7a2a") : r.eligibleOnly ? "#677167" : "#c8860a",
                    }} />
                    <span style={{ fontWeight: 700, color: "#1a2e1a" }}>
                      {r.crop}{r.type ? ` · ${r.type}` : ""}
                      {showPractice
                        ? <span style={{ fontWeight: 600, color: "#5a7a5a", marginLeft: 3 }}>· {r.practice}</span>
                        : null}
                    </span>
                    {r.complete ? (
                      <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 12, color: CROP_COLOR[r.crop]||"#4a7a2a", fontWeight: 700 }}>
                        {r.ay && r.ay.approvedYield != null ? (
                          <>
                            {r.ay.approvedYield} {r.isSilage ? "ton" : yieldUnit({ crop: r.ay.crop || r.crop, type: r.type })}
                            <span style={{ fontSize: 10, color: "#aaa", marginLeft: 4 }}>@ {(r.ay.coverageLevel*100).toFixed(0)}%</span>
                            {(r.isSilage
                              ? r.ay.approvedYield < 8
                              : (r.crop==="CORN"&&r.ay.approvedYield<50)||(r.crop==="SOYBEANS"&&r.ay.approvedYield<20)||(r.crop==="WHEAT"&&r.ay.approvedYield<20)||(r.crop==="DRY BEANS"&&r.ay.approvedYield<500)
                            ) && (
                              <span title={"Unusually low yield — verify this APH is correct."} style={{ marginLeft:6, fontSize:10, color:"#c8860a", fontWeight:800, cursor:"help" }}>⚠️ low</span>
                            )}
                          </>
                        ) : r.ppRow?.aphYield != null ? (
                          // PP-only: no crop_coverage row but the PP report carried
                          // an APH value (typically simple historical mean — Prelim
                          // not Apprv on R&H docs). Show it but flag the source.
                          <>
                            {r.ppRow.aphYield} {r.isSilage ? "ton" : yieldUnit({ crop: r.crop, type: r.type })}
                            {r.ppRow.coverageLevel != null && (
                              <span style={{ fontSize: 10, color: "#aaa", marginLeft: 4 }}>@ {(r.ppRow.coverageLevel*100).toFixed(0)}%</span>
                            )}
                            <span title={"From PP report (APH document not yet uploaded — re-upload APH PDF to get Apprv Yld)"} style={{ marginLeft:6, fontSize:10, color:"#7a5a2d", fontWeight:700, background:"#fef4e0", padding:"1px 5px", borderRadius:3, cursor:"help" }}>PP</span>
                          </>
                        ) : <span style={{ fontSize:11, color:"#c8860a", fontWeight:700 }}>PP loaded · APH pending</span>}
                      </span>
                    ) : r.eligibleOnly ? (
                      <span style={{ fontSize: 11, color: "#6a7a6a", fontWeight: 700, whiteSpace: "nowrap" }} title="PP-eligible crop — acreage history on file but not insured this year. APH is in the APH History tab.">
                        PP eligible{r.lastGrown ? <span style={{ fontWeight: 600, color: "#60735f" }}> · last grown {r.lastGrown.year}</span> : null}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: "#c8860a", fontWeight: 700 }}>missing</span>
                    )}
                    {/* SCO/ECO badges */}
                    {scoEnd && (
                      <span style={{ fontSize:10, fontWeight:800, padding:"2px 7px", borderRadius:4,
                        background:"#e8f4ff", color:"#1a4a8a", border:"1px solid #1a4a8a33",
                        marginLeft:2, whiteSpace:"nowrap" }}>
                        SCO {scoEnd.coverageLevel ? (scoEnd.coverageLevel*100).toFixed(0)+"%" : ""}
                      </span>
                    )}
                    {ecoEnd && (
                      <span style={{ fontSize:10, fontWeight:800, padding:"2px 7px", borderRadius:4,
                        background:"#f0e8ff", color:"#4a1a8a", border:"1px solid #4a1a8a33",
                        marginLeft:2, whiteSpace:"nowrap" }}>
                        ECO {ecoEnd.coverageLevel ? (ecoEnd.coverageLevel*100).toFixed(0)+"%" : ""}
                      </span>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SPRING VIEW  (Revenue Guarantee tab + Prevent Plant tab)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// FARMER APH HISTORY — Year-by-year production history from M13 P15A records
// ─────────────────────────────────────────────────────────────────────────────
function getAPHColor(rt) {
  if (!rt) return "#999";
  if ("A,PA,PW,WY,AY,BF,DA".includes(rt)) return "#2d6a1a";
  if ("T,C,L,N,P,TA".includes(rt)) return "#7a5a00";
  return "#999";
}
function isAPHExcluded(rt) {
  return !("A,PA,PW,WY,AY,BF,DA,T,C,L,N,P,TA".includes(rt||"?"));
}

function FarmerAPHHistory({ farmer }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCrop, setFilterCrop] = useState("all");

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const policyIds = (farmer.allPolicyIds || farmer._allPolicyIds || []);
        if (!policyIds.length) { setLoading(false); return; }
        const filter = policyIds.join(",");
        const data = await sbFetch("production_history", {
          filters: [`policy_id=in.(${filter})`],
          select: "crop_year,crop,county,type,practice,acres,yield_per_acre,record_type,excluded_from_avg",
        });
        setRows(data || []);
      } catch(e) { console.warn("APH history load error:", e); }
      setLoading(false);
    }
    load();
  }, [farmer.id]);

  const allCrops = [...new Set(rows.map(r => r.crop).filter(Boolean))].sort();

  const filtered = filterCrop === "all" ? rows : rows.filter(r => r.crop === filterCrop);

  // Group by crop + county + type + practice, then sort years within each group
  const groups = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const key = `${r.crop}|${r.county}|${r.type||""}|${r.practice||""}`;
      if (!g[key]) g[key] = { crop: r.crop, county: r.county, type: r.type, practice: r.practice, years: {} };
      g[key].years[r.crop_year] = r;
    }
    return Object.values(g).sort((a,b) => {
      if (a.crop !== b.crop) return (a.crop||"").localeCompare(b.crop||"");
      return (a.county||"").localeCompare(b.county||"");
    });
  }, [filtered]);

  // Get all years present, sorted descending
  const allYears = [...new Set(filtered.map(r => r.crop_year).filter(Boolean))].sort((a,b) => b-a);

  // Unit for a group's yields, matching the on-screen render logic.
  const groupUnit = (g) => normalizeCropType(g.type) === '026' ? 'ton'
    : ['CABBAGE','POTATOES','SWEET CORN','PROCESSING BEANS'].includes((g.crop||'').toUpperCase()) ? 'cwt' : 'bu';

  // Export the currently-filtered APH history to a print-styled HTML doc the
  // user can Save-as-PDF. Same window.open + window.print() pattern used by the
  // LRP monthly summary and the MI report — no new dependency.
  const exportPDF = useCallback(() => {
    const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
    const LOGO_URL = origin + "/Clover_Ag_Logo1_transparent.png";
    const printDate = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

    const groupsHtml = groups.map(g => {
      const unit = groupUnit(g);
      const yearsRows = allYears.map((yr, yi) => {
        const r = g.years[yr];
        if (!r) return `<tr class="${yi%2 ? 'alt' : ''}"><td class="yr">${yr}</td><td colspan="3" class="empty">—</td></tr>`;
        const rt = r.record_type || "";
        const excluded = isAPHExcluded(rt);
        const yld = r.yield_per_acre != null ? `${r.yield_per_acre} ${unit}` : "—";
        const acres = r.acres != null ? `${Number(r.acres).toLocaleString('en-US',{maximumFractionDigits:0})} ac` : "—";
        return `<tr class="${yi%2 ? 'alt' : ''}${excluded ? ' excl' : ''}">`
          + `<td class="yr">${yr}</td>`
          + `<td class="num yld${excluded ? ' strike' : ''}">${esc(yld)}</td>`
          + `<td class="num acres">${esc(acres)}</td>`
          + `<td class="rt"><span class="rt-badge${excluded ? ' rt-excl' : ''}">${esc(rt || "—")}</span></td>`
          + `</tr>`;
      }).join("");
      const prac = g.practice && g.practice !== "Non-Irrigated"
        ? `<span class="prac">${esc(g.practice)}</span>` : "";
      return `<div class="group">
        <div class="group-head"><span class="g-crop">${esc(g.crop)}${g.type ? ` &middot; ${esc(g.type)}` : ""}</span><span class="g-cty">${esc(g.county)} County</span>${prac}</div>
        <table class="aph-table">
          <thead><tr><th>Year</th><th class="num">Yield/Acre</th><th class="num">Acres</th><th class="rt">Type</th></tr></thead>
          <tbody>${yearsRows}</tbody>
        </table>
      </div>`;
    }).join("");

    const cropLabel = filterCrop === "all" ? "All crops" : filterCrop;
    const yrSpan = allYears.length ? `${allYears[allYears.length-1]}–${allYears[0]}` : "";

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>APH History — ${esc(farmer.name || "Farmer")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; color: #1a2e1a; margin: 0; padding: 32px 36px; background: #fff; }
  .header { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #2d6a2d; padding-bottom: 16px; margin-bottom: 22px; }
  .logo { height: 52px; }
  .title-block { flex: 1; }
  .title { font-size: 21px; font-weight: 800; color: #1a2e1a; }
  .subtitle { font-size: 13px; color: #5a7a5a; margin-top: 2px; }
  .asof { font-size: 11px; color: #677167; margin-top: 4px; }
  .print-btn { background: #2d6a2d; color: #fff; border: none; border-radius: 7px; padding: 9px 16px; font-size: 13px; font-weight: 700; cursor: pointer; }
  .legend { display: flex; gap: 18px; margin-bottom: 18px; flex-wrap: wrap; font-size: 11px; }
  .legend .lg { display: flex; align-items: center; gap: 5px; }
  .legend .lg .sw { border-radius: 4px; padding: 1px 7px; font-weight: 700; font-family: monospace; }
  .group { margin-bottom: 18px; border: 1px solid #d6e3d6; border-radius: 9px; overflow: hidden; page-break-inside: avoid; }
  .group-head { background: #f4fbf2; padding: 9px 14px; display: flex; gap: 10px; align-items: center; border-bottom: 1px solid #e0ece0; }
  .g-crop { font-weight: 800; font-size: 14px; color: #1a2e1a; }
  .g-cty { font-size: 12px; color: #5a7a5a; }
  .prac { font-size: 11px; color: #5a7a5a; background: #e8f2e8; border-radius: 5px; padding: 1px 8px; }
  .aph-table { width: 100%; border-collapse: collapse; }
  .aph-table th { background: #fafcfa; padding: 7px 14px; text-align: left; font-size: 10px; font-weight: 700; color: #677167; letter-spacing: 0.06em; text-transform: uppercase; }
  .aph-table th.num { text-align: right; }
  .aph-table th.rt { text-align: center; }
  .aph-table td { padding: 7px 14px; border-top: 1px solid #f0f4f0; font-size: 13px; }
  .aph-table tr.alt td { background: #fafcfa; }
  .aph-table tr.excl td { background: #fdfaf4; }
  .aph-table td.yr { font-weight: 800; font-family: monospace; color: #1a2e1a; width: 70px; }
  .aph-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .aph-table td.yld { font-weight: 700; color: #2d6a1a; }
  .aph-table td.acres { color: #5a7a5a; }
  .aph-table td.strike { color: #bbb; text-decoration: line-through; }
  .aph-table td.empty { text-align: center; color: #ccc; }
  .aph-table td.rt { text-align: center; }
  .rt-badge { background: #2d6a1a18; color: #2d6a1a; border: 1px solid #2d6a1a44; border-radius: 5px; padding: 2px 8px; font-size: 11px; font-weight: 700; font-family: monospace; }
  .rt-excl { background: #f5f5f5; color: #bbb; border-color: #e0e0e0; }
  .footer { margin-top: 22px; padding-top: 14px; border-top: 1px solid #d6e0d6; display: flex; justify-content: space-between; font-size: 10.5px; color: #60735f; }
  .footer .brand { font-weight: 700; color: #2d6a2d; }
  @media print { .no-print { display: none !important; } body { padding: 0; } }
</style></head>
<body>
  <div class="header">
    <img class="logo" src="${LOGO_URL}" alt="Clover Ag Risk Management" onerror="this.style.display='none'"/>
    <div class="title-block">
      <div class="title">APH Production History</div>
      <div class="subtitle">${esc(farmer.name || "Farmer")} &middot; ${esc(cropLabel)}${yrSpan ? ` &middot; ${yrSpan}` : ""}</div>
      <div class="asof">${rows.length} records &middot; Source: M13 production history (P15A) &middot; As of ${printDate}</div>
    </div>
    <button class="print-btn no-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
  </div>
  <div class="legend">
    <span class="lg"><span class="sw" style="background:#eef6ec;color:#2d6a1a;border:1px solid #2d6a1a44;">A</span> Actual</span>
    <span class="lg"><span class="sw" style="background:#fdf5e4;color:#7a5a00;border:1px solid #7a5a0044;">T</span> Transitional/Assigned</span>
    <span class="lg"><span class="sw" style="background:#f5f5f5;color:#999;border:1px solid #99944;">Z</span> Zero/Excluded</span>
  </div>
  ${groupsHtml}
  <div class="footer">
    <div class="brand">Clover Ag Risk Management</div>
    <div>Generated ${printDate}</div>
  </div>
  <script>window.addEventListener('load', function(){ setTimeout(function(){ try { window.print(); } catch(e){} }, 500); });</script>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) { alert("Popup blocked — allow popups for this site, then click Export again."); return; }
    writePrintWindow(w, html);
  }, [groups, allYears, filterCrop, farmer.name, rows.length]);

  if (loading) return <div style={{ textAlign:"center", padding:"40px 0", color:"#60735f" }}>Loading production history...</div>;

  if (!rows.length) return (
    <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #c8d8c8" }}>
      <div style={{ fontSize:32, marginBottom:10 }}>📊</div>
      <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:6 }}>No production history yet</div>
      <div style={{ fontSize:13, color:"#60735f" }}>Production history will appear here after the next M13 sync.</div>
    </Card>
  );

  return (
    <div>
      {/* Header + crop filter */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
        <div style={{ fontSize:13, color:"#60735f" }}>
          Year-by-year production from M13 · {rows.length} records · {allYears.length > 0 ? `${allYears[allYears.length-1]}–${allYears[0]}` : ""}
        </div>
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
          {allCrops.length > 1 && (
            <select value={filterCrop} onChange={e => setFilterCrop(e.target.value)}
              style={{ padding:"6px 10px", border:"1px solid #dde5dd", borderRadius:7, fontSize:12, color:"#1a2e1a", background:"#fff" }}>
              <option value="all">All crops</option>
              {allCrops.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <button onClick={exportPDF} title="Export this APH history to a PDF"
            style={{ padding:"6px 12px", border:"1px solid #2d6a2d", borderRadius:7, fontSize:12, fontWeight:700, color:"#fff", background:"#2d6a2d", cursor:"pointer", whiteSpace:"nowrap" }}>
            📄 Export PDF
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display:"flex", gap:16, marginBottom:12, flexWrap:"wrap" }}>
        {[["A","Actual","#2d6a1a","#eef6ec"],["T","Transitional/Assigned","#7a5a00","#fdf5e4"],["Z","Zero/Excluded","#999","#f5f5f5"]].map(([k,l,c,bg]) => (
          <div key={k} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11 }}>
            <span style={{ background:bg, color:c, border:`1px solid ${c}44`, borderRadius:4, padding:"1px 6px", fontWeight:700, fontVariantNumeric: "tabular-nums" }}>{k}</span>
            <span style={{ color:"#60735f" }}>{l}</span>
          </div>
        ))}
      </div>

      {/* Groups */}
      {groups.map((g, gi) => (
        <div key={gi} style={{ marginBottom:20, border:"1px solid #e4ece4", borderRadius:10, overflow:"hidden" }}>
          {/* Group header */}
          <div style={{ background:"#f4fbf2", padding:"10px 16px", display:"flex", gap:10, alignItems:"center", borderBottom:"1px solid #e4ece4" }}>
            <span style={{ fontWeight:800, fontSize:14, color:"#1a2e1a" }}>{g.crop}{g.type ? ` · ${g.type}` : ""}</span>
            <span style={{ fontSize:12, color:"#60735f" }}>{g.county} County</span>
            {g.practice && g.practice !== "Non-Irrigated" && (
              <span style={{ fontSize:11, color:"#5a7a5a", background:"#e8f2e8", borderRadius:5, padding:"1px 7px" }}>{g.practice}</span>
            )}
          </div>
          {/* Year rows */}
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:"#fafcfa" }}>
                <th style={{ padding:"8px 16px", textAlign:"left", fontSize:11, fontWeight:700, color:"#677167", letterSpacing:"0.06em", textTransform:"uppercase", width:80 }}>Year</th>
                <th style={{ padding:"8px 16px", textAlign:"right", fontSize:11, fontWeight:700, color:"#677167", letterSpacing:"0.06em", textTransform:"uppercase" }}>Yield/Acre</th>
                <th style={{ padding:"8px 16px", textAlign:"right", fontSize:11, fontWeight:700, color:"#677167", letterSpacing:"0.06em", textTransform:"uppercase" }}>Acres</th>
                <th style={{ padding:"8px 16px", textAlign:"center", fontSize:11, fontWeight:700, color:"#677167", letterSpacing:"0.06em", textTransform:"uppercase", width:80 }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {allYears.map((yr, yi) => {
                const r = g.years[yr];
                if (!r) return (
                  <tr key={yr} style={{ borderTop:"1px solid #f0f4f0", background: yi%2===0?"#fff":"#fafcfa" }}>
                    <td style={{ padding:"8px 16px", fontWeight:700, fontSize:13, color:"#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{yr}</td>
                    <td colSpan={3} style={{ padding:"8px 16px", color:"#ccc", fontSize:12, textAlign:"center" }}>—</td>
                  </tr>
                );
                const rt = r.record_type || "";
                const color = getAPHColor(rt);
                const isExcluded = isAPHExcluded(r.record_type);
                return (
                  <tr key={yr} style={{ borderTop:"1px solid #f0f4f0", background: isExcluded ? "#fdfaf4" : yi%2===0?"#fff":"#fafcfa" }}>
                    <td style={{ padding:"8px 16px", fontWeight:800, fontSize:13, color:"#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{yr}</td>
                    <td style={{ padding:"8px 16px", textAlign:"right", fontWeight:700, fontSize:14, color: isExcluded ? "#bbb" : color, textDecoration: isExcluded ? "line-through" : "none" }}>
                      {r.yield_per_acre != null ? `${r.yield_per_acre} ${normalizeCropType(g.type) === '026' ? 'ton' : ['CABBAGE','POTATOES','SWEET CORN','PROCESSING BEANS'].includes((g.crop||'').toUpperCase()) ? 'cwt' : 'bu'}` : "—"}
                    </td>
                    <td style={{ padding:"8px 16px", textAlign:"right", fontSize:13, color:"#5a7a5a" }}>
                      {r.acres != null ? `${Number(r.acres).toLocaleString('en-US',{maximumFractionDigits:0})} ac` : "—"}
                    </td>
                    <td style={{ padding:"8px 16px", textAlign:"center" }}>
                      <span style={{ background: isExcluded ? "#f5f5f5" : `${color}18`, color: isExcluded ? "#bbb" : color, border:`1px solid ${isExcluded ? "#e0e0e0" : color+"44"}`, borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700, fontVariantNumeric: "tabular-nums" }}>
                        {rt || "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FARMER CLAIMS HISTORY — current-year payments + multi-year loss record
// ─────────────────────────────────────────────────────────────────────────────
// Two data sources, both from M13/NAU sync:
//   1. `indemnities` (P20/P20A) — claim payments. M13 only sends current-year
//      claims, so dollar amounts are 2026-only for now. Prior crop years won't
//      have payment rows here.
//   2. `production_history` (P15/P15A) — yield records keyed by record_type.
//      Of the legacy field-8 codes, only PP/PW (Prevent Plant variants)
//      reliably indicate a claim event. The other codes we used to treat
//      as "loss" — I, IL, L, N, E, S, C — are actually **Assigned Yields
//      / County Yields**: RMA assigns the county-average yield as a
//      placeholder when a farmer hasn't grown that crop in that county
//      before, until they build their own 4-year APH database from
//      actual production. Per audit on 2026-05-01, 100% of these rows
//      have zero acres AND zero of them ever pair with production_status
//      ='M' (the RMA-spec indemnity flag) — confirming they're database
//      placeholders, not claims.
//      So the legitimate claim flags are:
//        • production_status ∈ {M, O, I, J, K, L} (P15A field 17, RMA spec)
//        • record_type ∈ {PP, PW} (Prevent Plant from M13 internal codes)
// ─────────────────────────────────────────────────────────────────────────────
const LOSS_RECORD_TYPES = new Set(["PP","PW"]);
const LOSS_TYPE_LABEL = {
  PP: "Prevented Plant",
  PW: "PP (Wheat replant)",
};
const PAYMENT_LOSS_CODE_LABEL = {
  V: "Verified Loss",
  L: "Loss",
  Q: "Quality Adjustment",
  D: "Unfunded Escrow",
  E: "Escrow Funded",
};

// RMA cause-of-loss codes carried in M13 P12 (Notice of Loss) records at
// field 5. Sourced from RMA M13 spec — common codes covered, less common
// ones (32-89) fall through to "Code N" labeling.
const CAUSE_OF_LOSS_LABEL = {
  "01": "Cold Wet Weather",
  "02": "Cold Winter",
  "03": "Cyclone",
  "04": "Drought",
  "05": "Earthquake",
  "06": "Excess Moisture / Precipitation / Rain",
  "07": "Failure of Irrigation Supply",
  "08": "Fire",
  "09": "Flood",
  "10": "Frost",
  "11": "Freeze",
  "12": "Hail",
  "13": "Hot Wind",
  "14": "Hurricane / Tropical Depression",
  "15": "Insects",
  "16": "Lightning",
  "17": "Plant Disease",
  "18": "Tornado",
  "19": "Volcanic Eruption",
  "20": "Wildlife",
  "21": "Wind / Excess Wind",
  "22": "House Burn",
  "31": "Decline in Price",
  "41": "Other (Snow / Lightning / etc.)",
  "51": "Failure to Pollinate",
  "65": "Excess Heat",
  "67": "Cold Injury",
  "88": "Other Causes",
  "91": "Other (Prevent Plant)",
  "92": "Other Coverage Specific",
  "93": "Other (PP Specific)",
};

// ── M13 DAMAGE CAUSE CODES — a DIFFERENT numbering, do not merge with the above.
//
// CAUSE_OF_LOSS_LABEL is an ALPHABETICAL list numbered 01..22 (Cold Wet Weather,
// Cold Winter, Cyclone, Drought, Earthquake, Excess Moisture…). RMA's actual
// codes are not alphabetical and not sequential, and the two COLLIDE on the
// codes our book actually carries:
//
//      code   in our data   CAUSE_OF_LOSS_LABEL says   RMA says
//        31          215     Decline in Price           EXCESS MOISTURE/PRECIP
//        11            7     Freeze                     DROUGHT
//        12            ·     Hail                       HEAT
//        51            ·     Failure to Pollinate       FLOOD
//        91            ·     Other (Prevent Plant)      FIRE
//
// Feeding P58/P21 damage causes into the old map would have printed 215 notices
// of excess moisture as "Decline in Price" on a grower-facing claims screen.
//
// SOURCE: RMA ICE **D00075** IceDamageCause, RY2026, read verbatim from RMA's
// own public reference directory:
//   pubfs-rma.fpac.usda.gov/pub/References/insurance_control_elements/PASS/2026/
//     2026_D00075_IceDamageCause_YTD.txt
//
// ⚠ THIS LIST WAS LISTED IN CLAUDE.md AS ONE OF "the ICE code lists we render
// raw for want of them — ask NAU or R&H". It is PUBLIC and always was. The
// whole PASS/<year>/ directory is browsable and holds 165 ICE files, including
// D00051 (guarantee adjustment type), D00073 (stage), D00067 (insured
// intention) and D00122 (CLU determination). Look there BEFORE asking an AIP.
//
// What the old hand-assembled map (RY2000 exhibit, revised 2018) had wrong:
//   55  said "GRP / GRIP crops only"  -> the retired product names. It is
//       ARPI/SCO/ECO/STAX/MP/PACE/MCO, and 55 is one of the most common codes
//       in this book (432 rows), so the wrong label was on screen constantly.
//   70  "Asiatic Citrus Canker", 90 "Force Fire", 96 "Fruit Set Failure"
//       -> not in RY2026 at all.
//   82  "Mycotoxin (Aflatoxin)" -> RMA says just "Mycotoxin".
//   and 22 codes were missing outright (09, 15, 46, 47, 52, 53, 58, 65, 66,
//   67, 72, 73, 74, 76, 80, 84, 85, 86, 87, 88, 89).
//
// The UI convention is unchanged: the CODE prints first and the label is a
// secondary hint, so an unlabelled code is still readable and a wrong label can
// never hide it.
const M13_DAMAGE_CAUSE = {
  "00": "No Indemnity",
  "01": "Decline in Price",
  "09": "Asian Soybean Rust",
  "11": "Drought",
  "12": "Heat",
  "13": "Failure of Irrigation Supply",
  "14": "Failure of Irrigation Equipment",
  "15": "Inability to Prepare Land for Irrigation",
  "21": "Hail",
  "22": "Excess Sun",
  "31": "Excess Moisture/Precipitation/Rain",
  "41": "Frost",
  "42": "Freeze",
  "43": "Cold Winter",
  "44": "Cold Wet Weather",
  "45": "Insufficient Chilling Hours",
  "46": "Tropical Storm (HIP-WI only)",
  "47": "Hurricane (HIP-WI only)",
  "51": "Flood",
  "52": "Low Salinity Caused by Excessive Rainfall",
  "53": "Nor'easter",
  "55": "ARPI/SCO/ECO/STAX/MP/PACE/MCO Crops Only",
  "58": "Post Bloom Fruit Drop",
  "61": "Wind/Excess Wind",
  "62": "Hot Wind",
  "63": "Cyclone",
  "64": "Tornado",
  "65": "Tidal Wave/Tsunami",
  "66": "Oxygen Depletion",
  "67": "Storm Surge",
  "71": "Insects",
  "72": "Contamination",
  "73": "Predation",
  "74": "Ice Flow",
  "76": "Salinity",
  "80": "Disease, Aquaculture",
  "81": "Plant Disease",
  "82": "Mycotoxin",
  "84": "Fire Blight",
  "85": "Federal or State Ordered Destruction",
  "86": "Quarantine",
  "87": "Falling Numbers",
  "88": "Hot Hemp",
  "89": "FIP-SI Smoke",
  "91": "Fire",
  "92": "Hurricane, Tropical Depression, Tropical Storm (not HIP-WI)",
  "93": "Wildlife",
  "95": "House burn (Pole burn)",
  "97": "Earthquake",
  "98": "Volcanic Eruption",
  "99": "Other (Snow, Lightning, Etc.)",
};
// "31,42" -> "31 excess moisture · 42 freeze". Code always first, so an
// unlabelled code is still readable and a wrong label can never hide it.
const m13CauseText = (codes) => {
  const list = String(codes || "").split(/[^0-9]+/).filter(Boolean);
  if (!list.length) return null;
  return list.map((c) => {
    const k = c.padStart(2, "0");
    const label = M13_DAMAGE_CAUSE[k];
    return label ? `${k} ${label.toLowerCase()}` : `${k} (meaning not established)`;
  }).join(" · ");
};

// RMA P15A field-17 production status codes per the M13 spec. M = Claim
// for Indemnity (claim-flagged year — flag stays after payment), O =
// UUF/Third Party Damage, I/J/K/L = ARPI loss states. The parser captures
// this into production_status.
const ACTIVE_CLAIM_STATUSES = new Set(["M","O","I","J","K","L"]);
const PRODUCTION_STATUS_LABEL = {
  A: "Harvested · Sold",
  B: "Harvested · Farm-Stored",
  C: "Harvested · Pick/Daily Sales",
  D: "Harvested · Yield Monitor",
  E: "Harvested · Authorized",
  F: "Harvested · Livestock-Fed",
  G: "Harvested · Field Records",
  H: "Harvested · Other",
  I: "Unharvested & Destroyed (ARPI)",
  J: "Unharvested · Other Use (ARPI)",
  K: "Unharvested · Appraised (ARPI)",
  L: "Unappraised (ARPI)",
  M: "Claim for Indemnity",
  N: "Appraisal (non-loss)",
  O: "UUF / Third Party Damage",
  Z: "Zero Planted Acres",
};

// M13 claim-stage codes. Two independent stage-code dialects exist:
//   P20X (NAU only): PP / HAR / REP — used in our existing claim_stages
//   P21  (both AIPs): PF / H / R / UH / FL / P2 — newly ingested for
//                     per-(crop, county, stage) dollar attribution
// Codes not in this map render as the raw code in the UI — DO NOT add a label
// without seeing the code in actual M13 data and confirming meaning.
const CLAIM_STAGE_LABEL = {
  // NAU P20X dialect — these are the AIP's own workflow buckets, not ICE codes.
  PP: "Prevent Plant",         // confirmed (Kirby 2025 P20X)
  HAR: "Harvest Loss",         // confirmed (Kirby 2025 P20X)
  REP: "Replant",              // confirmed (Kirby 2025 P20X)
  // ── P21 dialect: the PP codes name the STAGE, NOT the grower's election ────
  // ⚠ A STAGE CODE MAY NEVER ASSERT AN ELECTION. `PF` is two different codes in
  // two different RMA lists, and reading one as the other put "Prevent Plant ·
  // 5% buy-up" on R L JEFFRES's live CY2026 corn claim for a buy-up he did not
  // buy (Kevin, 2026-09-04).
  //
  //   P14 f[37] "PF"  = the ELECTION — the prevent-plant buy-up the grower
  //                     purchased. Stored in crop_coverage.insurance_options and
  //                     labelled by INSURANCE_OPTION_NAME. This one is real.
  //   P21 f[15] "PF"  = the claim STAGE, in R&H's dialect. THIS map.
  //
  // RMA's own D00073 (RY2026, fetched verbatim) does say PF = "Prevented
  // Planting - Unplanted Acreage with 5% Buy Up Option" — the lookup was
  // correct and the FIELD is not populated from it. Six independent
  // measurements of the live book, 2026-09-04, all say the same thing:
  //
  //   1. ZERO of the 82 CY2026 R&H claims carrying stage PF/PT sit on a policy
  //      that elected PF — while 192 coverages in the book DO elect it. If the
  //      stage were the tier those two populations would overlap; they are
  //      disjoint.
  //   2. P2 NEVER appears alone: all 68 P2 claims also carry PF. A claim cannot
  //      be both "no buy-up" and "5% buy-up".
  //   3. PT (the 10% tier) is never observed at all.
  //   4. PF is R&H-ONLY (191 claims / 0 NAU); NAU writes "PP" in the same slot
  //      (505). A grower's election is not carrier-specific.
  //   5. Jeffres's own CY2026 options are QL,TA,YA,YC / QL,YA,YC / YA —
  //      populated, sync-format, and carrying no PF. Per the provenance rule the
  //      file has STATED his elections, and this is not among them.
  //   6. Our own sync MANUFACTURES the code: normalizeRHStageText() in
  //      api/m13-sync.js maps the P20X notice text "PREVENTED PLANT" -> "PF",
  //      with no election anywhere in that path.
  //
  // So all three render as the stage they are. The tier is a property of the
  // COVERAGE, not of the claim — and a claim spans several coverages that may
  // differ (Jeffres's corn spans three counties), so there is no single election
  // for a claim card to state even when every coverage is known. Read the
  // election from insurance_options on the coverage surface instead.
  P2: "Prevent Plant",   // D00073 P2/PF/PT differ only by buy-up tier, which
  PF: "Prevent Plant",   // this field does not reliably carry — see above. The
  PT: "Prevent Plant",   // raw code stays visible; only the tier claim is gone.
  R:  "Replant",               // D00073 "Replanted"; confirmed (NAU 2025 P21 — 11 records)
  // H / UH are RMA's "Harvested" / "Unharvested". They are DELIBERATELY shown
  // as one "Harvest Loss" row (see CLAIM_STAGE_GROUP): the AIP issues a single
  // harvest-loss check per (crop, county) and UH is the production-to-count
  // offset against the gross H calculation — Stein 2025 R&H, H $129,111 + 5×UH
  // summing to −$48,915 = $80,196 paid, matching the P20 header. Splitting them
  // on screen inflates the apparent indemnity and renders the negatives as "—".
  H:  "Harvest Loss",
  UH: "Harvest Loss",
  // UUF / third-party damage — RMA appraises the acreage and counts the
  // appraisal as production so the insurer does not pay for damage somebody
  // else caused. Not yet observed in this book; labelled because D00073 states
  // them, and the CY2026 Danielewicz worksheet ("Uninsurable DUI driver
  // damage") is exactly this shape.
  TA: "Third-party damage · appraised production",
  TH: "Third-party damage · harvested production",
  TZ: "Third-party damage · zero production",
  SCT: "Notice Filed",         // CONFIRMED 2026-06-15: NAU P20X claim-INITIATION / notice-of-loss record, NOT a loss stage and NOT in D00073. TRI ACRES open claim's only M13 record is one SCT row dated to the damage date (status O), no P12/P20/P21; on closed claims it rides alongside the real stage (HAR/PP/REP). formatStageParts suppresses SCT when a real loss stage is present.
  // FL is resolved through stageLabelFor() rather than mapped here — see the
  // note on that function. D00073 settles it for BOTH AIPs.
};

// RMA ICE **D00067** IceInsuredIntention, RY2026 — what the grower told the
// adjuster they intended to do with the crop, carried on the P58 Notice of
// Loss (field 26) and stored as indemnities.insured_intention_codes.
//
// ⚠ **`CS` IS THE CHOP/SILAGE MARKER, AND IT WAS ALREADY IN OUR DATABASE.**
// A 2026-08-02 session looked for a field that flags "grain corn chopped for
// silage", ruled out four candidates and concluded none existed — the note
// reads "DON'T guess a marker — dig." This is the marker, stated by RMA, and
// the column has been synced the whole time with nothing reading it.
//
// Measured 2026-09-01: CS appears on 14 CY2026 corn claims and 9 CY2025 ones,
// and on NOTHING else in the book — no other crop, no other year, no other
// meaning. Every CY2026 one is stage H, cause 11, notice type DL, filed
// 2026-08-26..08-31 on consecutive R&H loss keys.
//
// ⚠ IT IS LIVE-ONLY. A claim set to `Released` loses its P58-derived fields
// entirely — measured: 0 of 47 CY2025 and 0 of 14 CY2026 Released claims carry
// an intention, a notice date or a cause, while every other status has them.
// So a chop cohort must be CAPTURED while the claims are open; re-deriving it
// later from this column silently shrinks.
const M13_INSURED_INTENTION = {
  CC: "Plant a cover crop",
  CS: "Chop / silage",
  DM: "Direct marketed",
  DS: "Destroy",
  GN: "Graze (only after November 1)",
  HN: "Hay (only after November 1)",
  HT: "Harvest",
  HY: "Hay",
  LC: "Leave cover",
  OT: "Other / unknown",
  PC: "Plant to another crop",
  PS: "Pasture",
  RP: "Replant",
};
// "CS" -> "CS chop / silage". Code first, same convention as the cause codes:
// an unlabelled code stays readable and a wrong label can never hide it.
const m13IntentionText = (codes) => {
  const list = String(codes || "").split(/[^A-Za-z]+/).filter(Boolean);
  if (!list.length) return null;
  return list.map((c) => {
    const k = c.toUpperCase();
    const label = M13_INSURED_INTENTION[k];
    return label ? `${k} ${label.toLowerCase()}` : `${k} (meaning not established)`;
  }).join(" · ");
};
// Is this claim a grain crop the grower told the adjuster they would CHOP?
// One rule, so every surface asks the question the same way.
const claimIsChopIntent = (row) =>
  String((row && row.insured_intention_codes) || "").toUpperCase().split(/[^A-Z]+/).includes("CS");
// A farm is chopping if EITHER source says so. The notice mirror covers both
// carriers and survives a Released claim; the claim column is R&H-only and
// decays. Unioned, neither one's absence can hide a farm from the list.
const rowIsChop = (r) => !!(r && (r.chopNotice || claimIsChopIntent(r.claim)));

// ── THE SILAGE CLAIM STATE — ONE RESOLVER, EVERY CLAIM WATCH SURFACE (2026-09-18) ──
// Kevin's Claims screenshot had two looks for one idea: "● Open + 🌾 chop" on some rows and
// "🌾 chop appraisal filed" on others, so the reader had to reverse-engineer what separated
// them. The distinction is REAL and is kept: either the AIP has staged a claim row (its
// status is the fact), or a CS Notice of Loss is on file and the AIP has staged nothing yet
// — which must never be dressed as an "Open" claim, because there is no indemnity row and
// no dollar behind it. What changes is that the state is decided ONCE, here, and the row,
// the mobile card, both tiles and every filter read it. PAID only where the AIP's own
// status says Paid; every other staged status stays OPEN and prints the AIP's word.
const SILAGE_CLAIM_STATE = Object.freeze({
  NONE: "NO_SILAGE_NOTICE", NOTICE: "SILAGE_NOTICE_FILED", OPEN: "SILAGE_CLAIM_OPEN", PAID: "SILAGE_CLAIM_PAID" });
const silageClaimState = (r) => {
  if (!rowIsChop(r)) return SILAGE_CLAIM_STATE.NONE;
  if (!r.claim) return SILAGE_CLAIM_STATE.NOTICE;
  return String(r.claim.status) === "Paid" ? SILAGE_CLAIM_STATE.PAID : SILAGE_CLAIM_STATE.OPEN;
};
const silageHasClaim = (st) => st === SILAGE_CLAIM_STATE.OPEN || st === SILAGE_CLAIM_STATE.PAID;

// ── ONE SET OF WORDS FOR THE SILAGE LANE (Kevin, 2026-09-24) ────────────────
// *"Unify or normalize the wording for silage appraisal claims, some say open chop,
// some say chop appraisal."*
//
// ⚠ THE VOCABULARY WAS UNIFIED ON 2026-09-18 AND THE LABELS AROUND IT WERE NOT, which
// is why this came back. `silageClaimState` decides the state once and every surface
// reads it — but the TILE, the FILTER OPTION and the ROW PILL each spelled that state
// out by hand, so one idea still arrived in three phrasings on a single screen:
//
//   tile    "🌾 Silage appraisal (AIP claim)"      filter  "🌾 Silage appraisal — AIP claim"
//   tile    "🌾 Silage notice filed"                filter  "🌾 Silage notice filed — awaiting AIP claim"
//   pill    "🌾 Silage appraisal"
//
// Same class as everything else this file records about a rule written more than once:
// it drifts, and nothing fails when it does. The words live here now and the three
// surfaces read them, so they cannot disagree again.
//
// ⚠ THE SHORT AND LONG FORMS ARE BOTH KEPT, DELIBERATELY. A tile is a narrow column and
// a filter option is a full line; forcing one string into both is how a tile ends up
// truncated mid-word. What matters is that the same STEM names the same state — "Silage
// appraisal" is always the claim, "Silage notice" is always the notice — and that one
// edit changes every surface.
const SILAGE_WORDS = Object.freeze({
  TAG: "🌾 Silage appraisal",                                    // the row pill
  CLAIM_SHORT: "🌾 Silage appraisal",                            // tile
  CLAIM_LONG: "🌾 Silage appraisal — AIP claim",                 // filter option
  NOTICE_SHORT: "🌾 Silage notice",                              // tile
  NOTICE_LONG: "🌾 Silage notice — awaiting AIP claim",          // filter option
  EITHER_LONG: "🌾 Silage — appraisal or notice filed",
  NONE_LONG: "🌾 No silage appraisal — the call list",
  // What the tile's own sub-label says, so the short form is not ambiguous on its own.
  CLAIM_HINT: "AIP claim staged",
  NOTICE_HINT: "awaiting AIP claim",
});

// AIP-aware stage label. FL is the one code whose meaning differs by AIP (see
// note in CLAIM_STAGE_LABEL): NAU FL = ECO/SCO (verified), R&H FL = unconfirmed.
// Everything else is AIP-agnostic. Always resolve FL through here so R&H rows
// never inherit the NAU-only ECO label.
function stageLabelFor(code, aip) {
  // FL = the ECO/SCO area-plan loss on CROP policies — for BOTH AIPs. NAU was
  // verified to the penny vs NAU's register; R&H confirmed 2026-06-15 (R&H
  // released ECO that morning; before that R&H FL sat Paid-base/Pending-FL,
  // the classic unpaid-ECO signature, e.g. Bentley base $2,710 paid + FL
  // $7,655 pending). On PRF policies FL is the PRF rainfall payout, NOT ECO —
  // so callers MUST gate this by product type before using the label on a
  // non-crop row (claimLineRows checks g.productLabel === 'Crop'; the farmer
  // card gates via formatStageParts when policyType !== 'crop'). This function
  // itself is product-agnostic. `aip` kept for call-site symmetry.
  // SETTLED by RMA ICE D00073, RY2026: FL = "Final Loss - ARPI/SCO/ECO/STAX/
  // MP/HIP-WI/FIP-SI/PACE/MCO Crops Only". It is NOT AIP-specific — the
  // comment above previously read "R&H FL = unconfirmed", and RMA's own list
  // settles it for both. `aip` is kept for call-site symmetry only.
  if (code === 'FL') return 'ECO/SCO';
  return CLAIM_STAGE_LABEL[code] || code;
}

// Is this a CONFIRMED stage we can label with confidence? Flags undecoded codes
// (SCT, P2, …) so they render muted instead of masquerading as a known stage.
function isStageConfirmed(code, aip) {
  if (code === 'FL') return true;
  return Object.prototype.hasOwnProperty.call(CLAIM_STAGE_LABEL, code);
}

// P21 stage codes that should be grouped together for display purposes.
// UH ("uncovered harvest"?) is empirically a NEGATIVE production-to-count
// offset against the gross H calculation — the AIP issues one Harvest Loss
// check, and surfacing H + UH as separate rows would be misleading. Net
// them into a single Harvest Loss row per (crop, county) instead.
const CLAIM_STAGE_GROUP = {
  UH: "H",
};

// The STAGE TYPES a claim carries, grouped and labelled — "P2", "PF" and "PT"
// all read Prevent Plant; "UH" folds into "H" per CLAIM_STAGE_GROUP.
//
// Module scope because two surfaces ask the same question: the Claims table
// renders these, and the All Customers claim filter matches on them. ClaimsView
// adds ONE display-only case on top (a `D` disbursement backed by a P21 FL line
// reads ECO/SCO), which needs state this scope cannot see — so the shared rule
// is the stage mapping and the caller adds the special case, rather than two
// copies of the mapping.
function claimStageTypeSet(c) {
  // Stages are an MPCI/crop concept — PRF/DRP/LRP have none.
  if (c && c.policyType && c.policyType !== "crop") return [];
  if (!c || !c.claimStages) return [];
  const seen = new Set();
  for (const raw of String(c.claimStages).split(",").map(x => x.trim()).filter(Boolean)) {
    seen.add(CLAIM_STAGE_GROUP[raw] || raw);
  }
  // ⚠ DEDUPED ON THE CODE, NEVER ON THE LABEL, and the difference is not
  // cosmetic. "P2,PF" maps to Prevent Plant TWICE, so a label-level dedupe looks
  // like tidying — but ClaimsView joins this list into its GROUP KEY, whose own
  // comment says "never merge different stages". Measured on the live book:
  // fourteen (policy, crop year) cells hold BOTH a `P2,PF` claim and a `PF`
  // claim, so deduping would give them one key and fuse two distinct claims into
  // one row. The filter below matches with `.includes`, which does not care.
  return [...seen].map(code => stageLabelFor(code, c.aip));
}

// ─────────────────────────────────────────────────────────────────────────────
// HOW A CLAIM WAS PAID — one classifier, used by every claim surface
// ─────────────────────────────────────────────────────────────────────────────
// M13 settles ONE claim as several P20A disbursements, and the loss_code on each
// says how that piece actually moved. The dollar total alone can't answer the
// question a grower asks — "where's my check?" — because a claim can settle
// almost entirely against premium owed: Danielewicz's CY2026 corn PP is $44,987
// of which $1,589 was a check and $43,398 a premium credit.
//
// ONLY CODES WHOSE MEANING IS DOCUMENTED GET A WORD. Everything else renders as
// the raw code — same rule the claim STAGE codes have followed since FL/P2 were
// found (see CLAIM_STAGE_LABEL). A plausible-looking label on an undecoded code
// is indistinguishable from a real one once it's on the screen.
//
// Live counts (2026-08-10):
//   E / L   paper check                                    597 rows · $25.22M
//     └ CLAUDE.md M13 reference: E = actual check, L = also actual.
//   M / F   premium credit — applied against premium owed   332 rows · $2.08M
//     └ verified against the R&H claim register (Danielewicz corn PP:
//       $42,628 = $10,615 check + $32,013 M+F credit).
//   D       ESCROW — confirmed money the AIP has not funded yet; it reappears
//           later as a dated E check (verified 2026-06-16: 45 of 47 CY2025 D
//           escrows did exactly that).
//
//           ⚠ CORRECTED 2026-08-19: this used to read "ECO/SCO — a real
//           coverage that pays", and the ECO/SCO half was never sourced. It was
//           inferred from the sample the 2026-06-16 check was drawn from. The
//           escrow behaviour is verified; the ATTRIBUTION was not.
//
//           Measured over every D row in the book: 9 of 9 are PREVENT-PLANT
//           claims (stages PF / PP / P2,PF) across BOTH AIPs, and 4 of 9 sit on
//           a crop+county cell carrying NO SCO or ECO coverage at all — Gillette
//           Ag's Cayuga corn (MP-0938967, $7,879) among them, which is what
//           surfaced it. So D does not mean ECO/SCO; if it correlates with
//           anything here it is prevent plant, and even that is 9 rows.
//
//           P20A field 8 decodes from RMA ICE D00070 (Disbursement Loss), which
//           we do not hold. Until we do, D says only what is verified: escrow,
//           awaiting funding. Ask R&H or NAU for D00070 rather than re-guessing.
//   V / Q   LRP/DRP: the record IS the payout (confirmed Kevin 2026-06-15) —
//           but M13 states no check-vs-credit detail for it, so the method
//           reads "not stated", never "check".                24 rows · $355K
//   ⚠ THE FULL LIST IS PUBLISHED, AND THE FIRST READING OF "P" WAS WRONG.
//           P20A's business rules name the code "Credit Memo - Loss applied to
//           another Policy", and that phrasing invites the stronger reading —
//           that this indemnity settled a LOSS on another policy. Kevin blocked
//           it, correctly. RMA's Appendix III/M-13 ACCOUNTING NARRATIVE gives
//           the operational meaning and the whole code list with it:
//
//             "Credit Memo - Loss applied to premium to this policy for current
//              reinsurance year (M). For livestock use (V)
//              Credit Memo - Loss applied to premium on another policy for
//              current reinsurance year (P) For Livestock use (U),
//              administrative fees (F),
//              other (e.g. interest, loss applied to livestock premium, etc.)
//              (O) For Livestock use (Q), and
//              recovery of premium or overpaid indemnity for prior, current, or
//              subsequent reinsurance year (R) For Livestock use (T)"
//
//           and, on the Settlement Report:
//
//             "M-Memos are premium amounts due (by producer) that have been
//              deducted from a loss payment ... on the same policy. P-Credit
//              Memos occur when premium due on ANOTHER policy (in same
//              reinsurance year) is designated with a 'P' ... which enables the
//              amount to be deducted from a loss payment on the policy
//              incurring a loss."
//
//           https://pubfs-rma.fpac.usda.gov/pub/Publications/M13_Handbook/
//             2026/Approved/ACT_NARR.pdf   (same text in the 2019 edition)
//
//           So P is a PREMIUM deduction, not a loss settlement: the producer
//           owed premium on a different policy, and that amount came out of
//           this loss payment. It is M's sibling — same mechanism, other
//           policy — which is why the two must not read as different KINDS of
//           thing on screen.
//
//   ⚠ AND THE NARRATIVE RETIRES THE LAST THREE UNKNOWNS. O and R were rendered
//           raw here on the grounds that D00070 is not public. It is not — but
//           the narrative prints the same list, and it also corrects V and Q,
//           which this table called "settled · method not stated". They are the
//           LIVESTOCK forms of M and O, not a separate settlement route.
//
//   The crop/livestock pairing runs through the whole list, which is itself a
//   check on the reading: M/V, P/U, O/Q, R/T — and, from the P20A spec, the
//   escrow pair E (SRA) / L (LPRA). Measured in this book, every L, V and Q row
//   is a DRP or LRP policy and not one is a crop policy.
//
//   T and U do not appear in this book today and are listed for completeness;
//   N (a zero-amount code, per P20A) does not appear either.
const CLAIM_PAY_METHOD = {
  // ── the money actually moving ───────────────────────────────────────────
  E: { key:"check",  label:"check",             short:"check",   note:"Escrow Funded (SRA) — RMA's own name for M13 disbursement loss code E. The AIP funded it out of escrow and the record carries an escrow check number and check issued date, which is why it reads as a check. All 605 E rows in the book carry that date, exactly as the P20A spec requires." },
  L: { key:"check",  label:"check · livestock",  short:"check",   note:"Escrow Funded (LPRA) — the livestock counterpart of E, under the Livestock Price Reinsurance Agreement. All 25 L rows in the book are DRP or LRP, not one is a crop policy, and all 25 carry a check issued date." },
  D: { key:"escrow", label:"escrow · awaiting funding", short:"escrow", note:"Unfunded Escrow — RMA's own name for code D: 'used to process loss without funding escrow'. It settles later as a dated check under E or L. None of the 6 D rows carries a check date and all are Pending, exactly as the spec describes. Every D row in the book today is a prevent-plant claim." },

  // ── indemnity taken as a credit against PREMIUM ─────────────────────────
  // ⚠ M AND P ARE THE SAME MECHANISM AND STILL NEED SEPARATE KEYS. Grouping
  // them merged the two into one CREDIT figure on any claim carrying both —
  // Hidden Springs read "$10,655 CREDIT" and the fact that $7,318 of it paid
  // premium on a DIFFERENT policy disappeared, which is exactly the distinction
  // this correction exists to surface. Same mechanism, different policy, own
  // line.
  M: { key:"credit", label:"premium credit · this policy",    short:"credit",  note:"Credit Memo — loss applied to premium on THIS policy for the current reinsurance year (RMA's wording, code M). The indemnity settled, but as a reduction of premium owed rather than as cash." },
  V: { key:"credit", label:"premium credit · livestock",       short:"credit",  note:"The livestock form of M — loss applied to premium on this policy. All V rows in the book are DRP or LRP." },
  P: { key:"credit_other", label:"premium credit · another policy",  short:"premium, other policy",
       note:"Credit Memo — loss applied to premium on ANOTHER policy for the current reinsurance year (RMA's wording, code P). The producer owed premium on a different policy of theirs in the same reinsurance year, and that amount was deducted from this loss payment. It is M's sibling, not a payment of another policy's loss. 43 rows / $216,000 in the book." },
  U: { key:"credit_other", label:"premium credit · another policy (livestock)", short:"premium, other policy", note:"The livestock form of P. None in the book today." },

  // ── deductions that are not premium ─────────────────────────────────────
  F: { key:"fee",    label:"administrative fee", short:"admin fee", note:"Administrative fees (RMA's wording, code F). Measured independently before the citation was found: 151 of 162 F rows are exact multiples of $30, the buy-up fee, against 4 of 199 M rows — which is what first separated F from M in this app." },
  O: { key:"other",  label:"other deduction",    short:"other",   note:"'Other (e.g. interest, loss applied to livestock premium, etc.)' — RMA's own catch-all, code O. The narrative gives the category but not which of its members applies to a given row, so the amount is shown and not explained further. 26 rows / $8,267 in the book." },
  Q: { key:"other",  label:"other · livestock",  short:"other",   note:"The livestock form of O. All Q rows in the book are DRP or LRP." },
  R: { key:"recovery", label:"recovery",         short:"recovery", note:"'Recovery of premium or overpaid indemnity for prior, current, or subsequent reinsurance year' — RMA's wording, code R. 1 row / $10,291 in the book." },
  T: { key:"recovery", label:"recovery · livestock", short:"recovery", note:"The livestock form of R. None in the book today." },};
function claimPayMethod(lossCode) {
  const c = (lossCode || "").trim().toUpperCase();
  if (!c) return { key:"unknown", label:"method not stated", short:"unstated", note:"M13 carries no disbursement code for this row" };
  return CLAIM_PAY_METHOD[c] || { key:`code_${c}`, label:`code ${c}`, short:`code ${c}`, note:`M13 disbursement loss code ${c}, which is not in RMA's published Appendix III accounting narrative. Every code that narrative lists is decoded above; this one is shown raw rather than guessed at. ICE D00070 is the authoritative list and is distributed to AIPs rather than published — ask the AIP for it.` };
}

// ── What a claim's "Date" cell says ─────────────────────────────────────────
// A check date when a check was cut. Otherwise indemnities.claim_date, which the
// R&H sync fills with the P58 NOTICE date (RMA Appendix III P58 field 25, "the
// Date that the producer provided notice to the AIP") — NOT the date of loss,
// which is P58 field 24 and is stored as damage_date. Labelled "Noticed" only
// where it equals the row's own notice_date; where the sync fell back to P20X
// f[9] (whose meaning is not established) the bare date prints, unlabelled.
function claimDateCell(c, empty) {
  const chk = c && c.checkDate ? String(c.checkDate).slice(0, 10) : "";
  if (chk) return chk;
  const cd = c && c.claimDate ? String(c.claimDate).slice(0, 10) : "";
  if (!cd) return empty;
  const nd = c.noticeDate ? String(c.noticeDate).slice(0, 10) : "";
  return nd === cd ? "Noticed " + cd : cd;
}

// Break a claim's disbursements down by payment method so the caption under the
// amount ALWAYS reconciles to the amount printed above it. Only CONFIRMED rows
// count (Paid / Verified) — the same set the displayed dollar figure uses — so a
// pending determination can never inflate one side of the split.
const CLAIM_CONFIRMED_STATUS = new Set(["Paid", "Verified"]);
function claimPayMix(rows) {
  const by = new Map();
  for (const r of (rows || [])) {
    if (!CLAIM_CONFIRMED_STATUS.has(r.status)) continue;
    // Claims page rows carry indemnityAmount; the farmer card's disbursement
    // rows carry amount. One mixer serves both so the two screens can't drift.
    const amt = (r.indemnityAmount != null ? r.indemnityAmount : r.amount) || 0;
    if (!amt) continue;
    const m = claimPayMethod(r.lossCode);
    const g = by.get(m.key) || { ...m, amount:0, count:0 };
    g.amount += amt; g.count += 1;
    by.set(m.key, g);
  }
  const parts = [...by.values()].sort((a, b) => b.amount - a.amount);
  const total = parts.reduce((s, p) => s + p.amount, 0);
  // One method → name it ("check", "premium credit ×2"). Several → show the
  // dollars per method, because that split IS the answer ("$1,589 check +
  // $43,398 credit"). Nothing confirmed → no caption; the row shows "—".
  let label = null;
  if (parts.length === 1) {
    label = parts[0].count > 1 ? `${parts[0].label} ×${parts[0].count}` : parts[0].label;
  } else if (parts.length > 1) {
    label = parts.map(p => `$${Math.round(p.amount).toLocaleString()} ${p.short}`).join(" + ");
  }
  // Methods with no dollars attached — for surfaces that break a claim into
  // per-crop/per-stage lines. The dollar split is a CLAIM-level fact (M13 gives
  // no per-cell disbursement), so naming the methods there is honest where
  // printing "$43,398 credit" against one stage line would not be.
  const names = parts.map(p => p.label).join(" + ") || null;
  // ECO/SCO sitting in escrow is money the grower is owed on a coverage that
  // paid — it just hasn't been funded yet. It is NOT confirmed, so it stays out
  // of `total` and out of every paid figure, but a claim carrying one must not
  // render as a bare "—" either. Callers show it muted, labeled as escrow.
  const escrow = (rows || [])
    .filter(r => (r.lossCode || "").trim().toUpperCase() === "D")
    .reduce((s, r) => s + ((r.indemnityAmount != null ? r.indemnityAmount : r.amount) || 0), 0);
  const title = parts.length
    ? parts.map(p => `${p.label}: $${p.amount.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} — ${p.note}`).join("\n")
    : "";
  return { parts, total, label, names, escrow, title };
}

function FarmerClaimsHistory({ farmer, productFilter }) {
  const isMobile = useIsMobile();
  // ⚠ THERE IS NO `cropFilter`, AND ITS ABSENCE IS DELIBERATE — do not reinstate one.
  // This component used to take 'spring' | 'fall' and hide the other season's claims,
  // which removed $830,971 across 166 claims on 72 farms from whichever tab you
  // happened to open. See the note on `filteredGroups` for the measurement. A claims
  // history is a FARMER-level fact; the season tabs scope coverage, not money.
  //
  // productFilter (optional): 'crop' | 'lrp' | 'drp' | 'prf'. When set,
  // shows only claims whose policy_type matches — used for the per-product
  // Claims sections on LRPView/DRPView/PRFView so each page only surfaces
  // its own claims. Crop tabs pass 'crop' so non-MPCI claims (Sullivan's
  // PRF, Keem's DRP) don't get mixed into the row-crop claims display.
  // Unset = show everything (legacy behavior, used nowhere currently).
  const [prodRows, setProdRows]       = useState([]);
  const [indemRows, setIndemRows]     = useState([]);
  const [lossNotices, setLossNotices] = useState([]);
  const [premiumRows, setPremiumRows] = useState([]);   // unified premiums across products for loss-ratio
  const [lineRows, setLineRows]       = useState([]);   // P26 indemnity_lines (per-cell breakdown)
  const [policyCounty, setPolicyCounty] = useState({}); // policy_id → county (for DRP claim county)
  const [loading,  setLoading]        = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Claims History is FARMER-centric, not policy-centric. The user
        // expects to see every claim this farmer ever had — across all
        // crop years, all AIPs, all policy numbers — when they open this
        // tab. Each card in the dashboard is keyed by a single policy
        // number, but for claims we widen the lookup to every policy
        // associated with this farmer (via allFarmerDbIds → all policies
        // owned by any of those farmer-row ids → all indemnities).
        // Without this, Sparta's CY2025 NAU $171K claim and CY2026 R&H
        // pending entries don't show up on her CY2026 NAU card.
        const farmerDbIds = farmer.allFarmerDbIds || farmer._allFarmerIds || [farmer.id].filter(Boolean);
        let policyIds = farmer.allPolicyIds || farmer._allPolicyIds || [];
        // Declared out here so the premium unifier below can read it even when the
        // policies fetch fell through — an empty map costs a crop_year, not the premium.
        const pyear = {};
        if (farmerDbIds.length) {
          const fidFilter = farmerDbIds.join(",");
          try {
            const allPolicies = await sbFetch("policies", {
              filters: [`farmer_id=in.(${fidFilter})`],
              // crop_year comes from HERE because it is not on crop_coverage or
              // lrp_endorsements — asking those tables for it 400s the whole request.
              select: "id,county,crop_year",
            });
            const widePolicyIds = (allPolicies || []).map(p => p.id).filter(Boolean);
            if (widePolicyIds.length) {
              // Union with the locally-tracked ids so we never lose any
              policyIds = Array.from(new Set([...policyIds, ...widePolicyIds]));
            }
            // policy_id → county, used to surface DRP claim county (DRP
            // indemnity rows carry no county — cid is policy-level).
            const cmap = {};
            for (const p of (allPolicies || [])) if (p.id && p.county) cmap[p.id] = p.county;
            setPolicyCounty(cmap);
            // policy_id → crop_year, for the premium rows whose own tables do not carry it.
            for (const p of (allPolicies || [])) if (p.id && p.crop_year != null) pyear[p.id] = +p.crop_year;
          } catch (e) { /* fall back to local policyIds */ }
        }
        if (!policyIds.length) { setLoading(false); return; }
        const idFilter = policyIds.join(",");
        const [prod, indem, notices, lines, ccPrem, lrpPrem, drpPrem, prfIvls] = await Promise.all([
          sbFetch("production_history", {
            filters: [`policy_id=in.(${idFilter})`],
            select: "policy_id,crop_year,crop,county,type,practice,acres,production,yield_per_acre,record_type,production_status,adjusted_yield,adjusted_production",
          }),
          sbFetch("indemnities", {
            filters: [`policy_id=in.(${idFilter})`],
            select: "claim_number,loss_code,indemnity_amount,check_date,claim_date,status,policy_type,aip,policy_id,source_file,created_at,crop,county,claim_total,claim_stages,cid,adjuster,loss_key,crop_year,quarter,damage_cause_codes,notice_type_code,damage_date,notice_date,insured_intention_codes",
          }),
          sbFetch("loss_notices", {
            filters: [`policy_id=in.(${idFilter})`],
            select: "policy_id,iid,cause_code,amount,loss_event_date,crop_year,aip",
          }).catch(() => []),
          // P26 per-cell line items + P21 stage-attributed lines. The `source`
          // column distinguishes ('p26' vs 'p21'); `stage_code` is set on
          // p21 rows. Both are mandatory for the per-(crop, county, stage)
          // table render — omitting them was the bug that made Kirby's
          // claim render as a single fallback row even after backfill.
          sbFetch("indemnity_lines", {
            filters: [`policy_id=in.(${idFilter})`],
            select: "policy_id,iid,cid,unit_id,line_key,loss_key_base,line_seq,line_date,stage_code,source,acres,indemnity_calc,indemnity_actual,crop,county,crop_year,aip,source_file,damage_cause_1,damage_pct_1,damage_cause_2,damage_pct_2,damage_cause_3,damage_pct_3",
          }).catch(() => []),
          // Premium rows for loss-ratio. Each product family has its own
          // table; we fetch them in parallel and union them in the
          // aggregator below. crop_year on each is the policy crop_year.
          sbFetch("crop_coverage", {
            filters: [`policy_id=in.(${idFilter})`],
            // ⚠ NO crop_year AND NO farmer_premium ON THIS TABLE. Both were asked for,
            // both 400 the request, and the .catch below turned that into an empty
            // array — so the crop half of every loss ratio was silently missing. The
            // producer figure is `net_premium`; the year comes from the policy.
            select: "policy_id,crop,county,total_premium,subsidy,net_premium,liability,acres",
          }).catch(() => []),
          sbFetch("lrp_endorsements", {
            filters: [`policy_id=in.(${idFilter})`],
            // Same again: lrp_endorsements carries no crop_year.
            select: "policy_id,producer_premium,head,target_weight,coverage_price",
          }).catch(() => []),
          sbFetch("drp_endorsements", {
            filters: [`policy_id=in.(${idFilter})`],
            select: "policy_id,soi_crop_year,producer_premium,liability",
          }).catch(() => []),
          sbFetch("prf_intervals", {
            filters: [`prf_policy_id=in.(${idFilter})`],
            select: "prf_policy_id,premium,liability",
          }).catch(() => []),
        ]);
        setProdRows(prod || []);
        setIndemRows(indem || []);
        setLossNotices(notices || []);
        setLineRows(lines || []);

        // Unify premiums into a single shape for loss-ratio aggregation.
        // Each row: { product, crop_year, crop, premium }. Crop is null
        // for non-MPCI products since those policies aren't crop-keyed.
        const unified = [];
        for (const r of (ccPrem || [])) {
          const fp = (r.net_premium != null ? Number(r.net_premium)
                    : (r.total_premium != null && r.subsidy != null) ? Number(r.total_premium) - Number(r.subsidy)
                    : Number(r.total_premium || 0));
          if (fp > 0) unified.push({ product: 'crop', crop_year: pyear[r.policy_id] ?? null, crop: r.crop, county: r.county, premium: fp });
        }
        for (const r of (lrpPrem || [])) {
          if (r.producer_premium > 0) unified.push({ product: 'lrp', crop_year: pyear[r.policy_id] ?? null, crop: null, premium: Number(r.producer_premium) });
        }
        for (const r of (drpPrem || [])) {
          if (r.producer_premium > 0) unified.push({ product: 'drp', crop_year: r.soi_crop_year, crop: null, premium: Number(r.producer_premium) });
        }
        // PRF: prf_intervals is keyed by prf_policy_id (a different table than policies)
        // so the join above is actually wrong but kept defensive. PRF premiums
        // typically arrive via prf_policies anyway — fall back to 0 if intervals miss.
        for (const r of (prfIvls || [])) {
          if (r.premium > 0) unified.push({ product: 'prf', crop_year: null, crop: null, premium: Number(r.premium) });
        }
        setPremiumRows(unified);
      } catch(e) { console.warn("Claims history load error:", e); }
      setLoading(false);
    }
    load();
  }, [farmer.id]);

  // Resolve the CROP YEAR (not the check year) for an indemnity row.
  // Priority:
  //   1. r.crop_year (set explicitly by parser — for backfills this is the
  //      M13 file year; for daily syncs it's the matched policy's year)
  //   2. source_file 'M13_YYYY_backfill' marker (legacy rows pre-crop_year)
  //   3. check_date.year as a last resort (NOT ideal — Brian Thompson's
  //      $22K check cut Jan 9 2026 is for CY2025 corn)
  const indemYear = (r) => {
    if (r.crop_year) return r.crop_year;
    if (r.source_file) {
      const m = r.source_file.match(/(\d{4})_backfill/);
      if (m) return parseInt(m[1]);
    }
    const dateStr = r.check_date || r.claim_date;
    if (dateStr) return new Date(dateStr).getFullYear();
    return null;
  };

  // DRP claims are grouped by the COVERAGE quarter's calendar year — a farmer
  // wants "what my Q1–Q4 2025 coverage triggered" in the 2025 bucket, even
  // though e.g. a Q4 2025 indemnity is paid in early 2026. The authoritative
  // quarter (indemnities.quarter, from M13 P28→P18) gives the coverage year
  // directly; fall back to check-date year, then crop_year. Other products
  // keep crop_year grouping.
  const paymentYearMode = (productFilter || '').toLowerCase() === 'drp';
  const quarterYear = (q) => { const m = String(q || "").match(/\b(20\d{2})\b/); return m ? parseInt(m[1]) : null; };
  const rowYear = (r) => {
    if (paymentYearMode) {
      return quarterYear(r.quarter)
        || (r.check_date ? parseInt(String(r.check_date).slice(0, 4)) : null)
        || indemYear(r);
    }
    return indemYear(r);
  };

  // Build the unified claims list. One row per indemnity disbursement, plus
  // production_history claim events (M-flag / PP / PW) that don't have a
  // matching indemnity (they're filed but no payment record yet).
  // Crop / county info gets attached from production_history when we can
  // match on (policy_id, year). Cause of loss comes from loss_notices.
  // WHERE THE MONEY WENT, per claim: the stage codes on the P21 worksheet lines.
  // Keyed (policy, claim number, crop year) — `loss_key_base` is what indemnity_lines
  // carries the claim number in, and the crop year is in the key because a claim number
  // REPEATS across years (NAU 00000064 exists in 2025 and 2026).
  // ⚠ P21 ONLY. p26 lines are the per-cell indemnity detail and carry no stage, so
  // including them would add an empty code and make every claim look un-staged.
  const p21StageByClaim = useMemo(() => {
    const m = new Map();
    for (const l of (lineRows || [])) {
      if (l.source !== "p21" || !l.stage_code) continue;
      const k = `${l.policy_id}|${String(l.loss_key_base || "").trim()}|${l.crop_year}`;
      if (!m.has(k)) m.set(k, new Set());
      m.get(k).add(String(l.stage_code).trim().toUpperCase());
    }
    return m;
  }, [lineRows]);

  const claimsRows = useMemo(() => {
    const rows = [];

    // STRICT POLICY: don't infer crop/county/cause from cross-table matches.
    // The M13 parser doesn't yet capture crop/county/PP-flag onto the
    // indemnities table — every row's crop/county column is currently NULL.
    // Until the parser is fixed, those columns render as `—` and the claim
    // type only carries information we can authoritatively source: the
    // policy_type (LRP/DRP/PRF) for non-MPCI products. For MPCI policies
    // we render "Crop Loss" — neutral, not a guess at PP vs Harvest.
    // (See feedback memory feedback_no_guessing.md — the previous version
    // matched indemnity rows to production_history by policy+year, which
    // tagged every disbursement with the first matching crop and led to
    // every Kirby row showing CORN.GSG and every Dunham PP claim showing
    // as "Harvest Loss".)
    const noticeFor = (policyId, year) => (lossNotices||[]).find(n =>
      n.policy_id === policyId && n.crop_year === year
    );

    // 1) From indemnities: one row per disbursement
    for (const r of indemRows) {
      const year = rowYear(r);
      const notice = noticeFor(r.policy_id, indemYear(r));
      const ptype = (r.policy_type||'').toLowerCase();
      const productLabel = ptype === 'lrp' ? 'LRP' :
                           ptype === 'drp' ? 'DRP' :
                           ptype === 'prf' ? 'PRF' : 'Crop';
      // Cause of loss: only meaningful for MPCI (Crop) policies. The RMA
      // cause-of-loss code table (drought, hail, etc.) applies to MPCI
      // Notices of Loss; for LRP/DRP/PRF the P12 f[5] field carries
      // something else (likely a product/category marker). Caught when
      // Keem's DRP-only policy was rendering "Excess Moisture" — that
      // can't be right because DRP is milk-revenue-based, not weather.
      // See feedback memory feedback_no_guessing.md.
      //
      // CORRECTED 2026-08-19. `notice.cause_code` comes from loss_notices, which
      // is built from P12 — and P12 is the PAYMENT record. Its f[5] is the
      // Payment Type Code, not a cause of loss, which is why this only ever
      // produced "00"/"06" and why the PP variants never appeared. It is also
      // why a DRP policy once rendered "Excess Moisture".
      //
      // The real cause is on the indemnity row now: P58 Notice of Loss #23 for
      // R&H, P21 #21/#24/#27 (the PAID lines) for NAU. Prefer it; fall back to
      // the old P12 reading only when the new column is empty, so nothing that
      // rendered before goes blank — but note the two use DIFFERENT numbering,
      // so each must go through its OWN map. See M13_DAMAGE_CAUSE.
      const m13Cause = m13CauseText(r.damage_cause_codes);
      const realCause = m13Cause
        || ((productLabel === 'Crop' && notice && notice.cause_code && notice.cause_code !== "00")
              ? (CAUSE_OF_LOSS_LABEL[notice.cause_code] || `Code ${notice.cause_code}`)
              : null);

      // Stage codes for this claim — comma-sep list from indemnities.claim_stages.
      // Mapped through CLAIM_STAGE_LABEL; unmapped codes pass through as-is.
      const stageCodes = (r.claim_stages || '').split(',').map(s => s.trim()).filter(Boolean);
      // ⚠⚠ `claim_stages` IS THE ADJUSTER'S NOTICE RECORD (P20X), NOT WHERE THE MONEY WENT.
      // CLAUDE.md already settled that a claim's CROP and COUNTY come from P21 because the
      // money decides — and recorded, in as many words, "Stages still union; only cids
      // don't." This is what that costs.
      //
      // Kevin, 2026-10-01, on Verratti Farms: *"it says verrattis claim was paid for both
      // Prevent Plant and harvest loss. This i believe is untrue, I think the payment was
      // for Prevent Plant only; they have an active harvest claim for silage appraisals."*
      // MEASURED that day: claim 00000081, paid 2026-08-12, $46,233 check + $21,544 credit
      // + $60 fee — and ALL NINETEEN of its P21 worksheet lines are stage `P2` (prevent
      // plant). There is no harvest line. `claim_stages` reads "HAR,PP" because the
      // adjuster OPENED a harvest stage that has never been paid.
      //
      // So the two are separated: the stages the MONEY is on, and the stages opened
      // without payment. That is strictly more informative than either label alone —
      // "paid on prevent plant, harvest stage open" is the true sentence, and it is
      // exactly the open silage claim Kevin is describing.
      //
      // ⚠ THE P20X FALLBACK IS LOAD-BEARING AND IS NOT TOUCHED. 95 open and 60 R&H
      // released claims carry no P21 at all; narrowing those would blank the column
      // rather than correct it. No P21 lines => paid stages are simply unknown, and the
      // row falls back to the stored union exactly as before.
      const moneyStages = [...(p21StageByClaim.get(
        `${r.policy_id}|${String(r.claim_number || '').trim()}|${r.crop_year}`) || [])];
      const _ps = claimPaidStages(stageCodes, moneyStages);
      const paidStageCodes = _ps.basis === "p21" ? _ps.paid : null;
      const openOnlyStageCodes = _ps.openOnly;

      rows.push({
        kind: 'payment',
        year,
        policyId: r.policy_id,
        crop: r.crop || null,                // authoritative from cid → covMap join (parser side)
        cropType: null,
        county: r.county || null,            // authoritative from cid → covMap join (parser side)
        cause: realCause,
        amount: Number(r.indemnity_amount) || 0,
        checkDate: r.check_date,
        noticeDate: r.notice_date || null,   // P58 field 25 NOTICE date — not the date of loss (that is damage_date); check_date is settlement only
        status: r.status,
        lossCode: r.loss_code,
        productLabel,
        claimNumber: r.claim_number,
        lossKey: r.loss_key || null,     // the AIP's claim identity — see claimGroups
        claimTotal: r.claim_total != null ? Number(r.claim_total) : null,
        stageCodes,                          // ['PP','HAR','REP'] etc — empty if no P20X/P21 detail
        // The stages the MONEY is on (P21), and the ones the notice opened without
        // payment. null = no P21 lines, so the paid stages are unknown and `stageCodes`
        // stands as before — see the note above.
        paidStageCodes,
        openOnlyStageCodes,
        adjuster: r.adjuster || null,
        cid: r.cid || null,
        aip: r.aip,
        quarter: r.quarter || null,          // DRP: authoritative coverage quarter (M13 P28→P18)
      });
    }

    // 2) From production_history (M-flagged or PP/PW) that don't already
    //    appear in the indemnity list — claims filed but unpaid in M13
    const indemKeySet = new Set(indemRows.map(r => `${r.policy_id}|${indemYear(r)}`));
    const phGroups = {};
    for (const r of prodRows) {
      const isClaim = (r.production_status && ACTIVE_CLAIM_STATUSES.has(r.production_status)) ||
                      LOSS_RECORD_TYPES.has(r.record_type);
      if (!isClaim) continue;
      const key = `${r.policy_id}|${r.crop_year}|${r.crop}|${r.county}|${r.type||''}`;
      if (!phGroups[key]) {
        phGroups[key] = {
          policyId: r.policy_id,
          year: r.crop_year,
          crop: r.crop,
          cropType: r.type,
          county: r.county,
          status: r.production_status || null,
          recordType: r.record_type || null,
          reportedYield: r.yield_per_acre,
          adjustedYield: r.adjusted_yield,
          acres: 0,
        };
      }
      const g = phGroups[key];
      if (r.acres != null) g.acres += Number(r.acres);
      const psSev = { M:5, O:4, I:3, J:3, K:3, L:3 };
      if ((psSev[r.production_status]||0) > (psSev[g.status]||0)) g.status = r.production_status;
      const rtSev = { PP:6, PW:6, IL:5, L:4, P:4, I:3 };
      if ((rtSev[r.record_type]||0) > (rtSev[g.recordType]||0)) g.recordType = r.record_type;
      if (r.adjusted_yield != null && g.adjustedYield == null) g.adjustedYield = r.adjusted_yield;
    }
    for (const g of Object.values(phGroups)) {
      // Skip if there's already an indemnity row for this policy+year —
      // the payment row already shows up in the table
      if (indemKeySet.has(`${g.policyId}|${g.year}`)) continue;
      const notice = noticeFor(g.policyId, g.year);
      // Same cause-code guard as above — skip "00" / blank
      const realCause = notice && notice.cause_code && notice.cause_code !== "00"
                        ? (CAUSE_OF_LOSS_LABEL[notice.cause_code] || `Code ${notice.cause_code}`)
                        : null;
      // production_history rows ARE crop-authoritative — they come straight
      // from P15A's parsed crop/county/type fields. PP detection here is
      // also authoritative because record_type='PP'/'PW' is the RMA spec
      // for Prevent Plant. So we DO keep crop/PP info on this side.
      const isPP = g.recordType === 'PP' || g.recordType === 'PW';
      rows.push({
        kind: 'filed',
        year: g.year,
        crop: g.crop,
        cropType: g.cropType,
        county: g.county,
        cause: realCause,
        amount: null,
        checkDate: null,
        status: 'Filed (no payment yet)',
        lossCode: null,
        productLabel: 'Crop',
        claimNumber: null,
        knownPP: isPP,                   // authoritative from P15A record_type
      });
    }

    // Newest first; within same year, paid before pending; within same
    // year+kind, descending by check date
    rows.sort((a, b) => {
      const yd = (b.year || 0) - (a.year || 0);
      if (yd) return yd;
      const ka = a.checkDate ? 1 : 0, kb = b.checkDate ? 1 : 0;
      if (ka !== kb) return kb - ka;
      return (b.checkDate || '').localeCompare(a.checkDate || '');
    });
    return rows;
  }, [prodRows, indemRows, lossNotices, p21StageByClaim]);

  // Tag rows with claim-type classification. Authoritative sources only:
  //   • policy_type='lrp'/'drp'/'prf' → that product label
  //   • indemnity row's claim_stages (M13 P20X for NAU, P21 f[15] for R&H)
  //     → mapped via CLAIM_STAGE_LABEL; multi-stage claims are joined with
  //     '+' (e.g. "Prevent Plant + Harvest Loss + Replant" for Kirby's mix)
  //   • Filed PP row (production_history record_type='PP'/'PW')
  //     → "Prevent Plant"
  //   • Otherwise → "Crop Loss" (neutral; we don't claim PP vs Harvest
  //     when no stage detail is on the row)
  const taggedRows = useMemo(() => {
    return claimsRows.map(r => {
      const ptype = (r.productLabel || '').toLowerCase();
      let claimType;
      let claimTypeRaw = null;     // for color tone selection
      if (ptype === 'lrp' || ptype === 'drp' || ptype === 'prf') {
        claimType = r.productLabel; // LRP / DRP / PRF
      } else if ((r.paidStageCodes && r.paidStageCodes.length) || (r.stageCodes && r.stageCodes.length > 0)) {
        // ⚠ THE STAGES THE MONEY IS ON, not the stages the adjuster opened. Verratti's
        // CY2026 claim read "Harvest Loss + Prevent Plant" on a payment whose nineteen
        // worksheet lines are all P2 — the harvest stage was opened and never paid. Where
        // P21 states the stages, it decides; where it does not, the stored P20X union
        // stands exactly as before. See the note on paidStageCodes.
        const source = (r.paidStageCodes && r.paidStageCodes.length) ? r.paidStageCodes : r.stageCodes;
        // De-dupe labels (PP and PF both → "Prevent Plant", so a mixed
        // R&H + NAU claim still de-duplicates cleanly)
        const labels = Array.from(new Set(
          source.map(c => stageLabelFor(c, r.aip))
        ));
        claimType = labels.join(' + ');
        // A stage the notice opened and the money never reached is NAMED, not dropped —
        // it is the open half of the claim and the reason the grower is still waiting.
        const openLabels = Array.from(new Set(
          (r.openOnlyStageCodes || []).map(c => stageLabelFor(c, r.aip))
        )).filter(l => l && !labels.includes(l));
        if (openLabels.length) claimType += ` · ${openLabels.join(' + ')} open, unpaid`;
        // Use the most-severe stage label for tone (PP > Harvest > Replant)
        if (labels.includes('Prevent Plant')) claimTypeRaw = 'Prevent Plant';
        else if (labels.includes('Harvest Loss')) claimTypeRaw = 'Harvest Loss';
        else claimTypeRaw = labels[0];
      } else if (r.knownPP) {
        claimType = 'Prevent Plant';
        claimTypeRaw = 'Prevent Plant';
      } else if (r.kind === 'filed') {
        // P15A flagged claim with no record_type='PP'/'PW' and no P20 yet.
        // Honest about uncertainty: don't render as "Crop Loss" because we
        // can't actually distinguish PP from Harvest from anything else
        // without stage data. Adjuster fills this in once they assess.
        claimType = 'Stage TBD';
        claimTypeRaw = 'Filed';
      } else {
        // Payment row (P20+P20A exists) but no P20X/P21 stage detail.
        // Rare — usually means an old archived claim that lost its stage
        // detail. "Crop Loss" is the closest neutral label.
        claimType = 'Crop Loss';
        claimTypeRaw = 'Crop Loss';
      }
      return { ...r, claimType, claimTypeRaw: claimTypeRaw || claimType };
    });
  }, [claimsRows]);

  // ── Group payment rows by claim_number ─────────────────────────────────
  // M13 disbursements are at the check level, but the user thinks at the
  // claim level ("Kirby had ONE big claim in 2025 for $159K"). Each claim
  // can have multiple disbursements (a few paid checks + reserve/escrow
  // ledger entries). Collapse to one row per claim with paid/pending
  // breakdown; full disbursement list available as expandable detail.
  const claimGroups = useMemo(() => {
    const byKey = new Map();

    for (const r of taggedRows) {
      // 'filed' rows (no payment yet) keyed by year+crop+county so each
      // unique production_history claim shows once
      // Payments key on LOSS_KEY — the AIP's actual claim identity. A NAU claim
      // number can carry two claims with two separate P20 totals (Sugar Branch
      // CY2025 00000059 = 844973582-1 at $195,246 plus -2 at $82,216); keying on
      // the number sums them into one $277,462 group whose claim_total field
      // still reads $195,246. Rows with no loss_key fall back to the number.
      // ⚠ THE LOSS KEY IS UNIQUE WITHIN A POLICY, NOT ACROSS THE BOOK, so the
      // POLICY belongs in the key. Measured 2026-09-22: 4 of 591 distinct loss
      // keys sit on more than one policy — DEMO-DRP-26 shares four with SPARTA
      // FARMS LP's real CY2025 claims. Those two are different FARMERS and this
      // component fetches `farmer_id=in.(…)`, so no group merges today (0 cases
      // where one farmer carries a duplicated loss_key + aip + year), and this
      // changes no rendered group. It is the same unscoped-key defect that was
      // producing four false variances in check-data's claims-tieout, closed
      // here too rather than left one data shape away from summing two policies'
      // claims into one row under a single farm.
      const key = r.kind === 'payment'
        ? `pay|${r.policyId || r.policy_id || ''}|${r.lossKey || r.claimNumber || 'no-claim'}|${r.aip || ''}|${r.year || ''}`
        : `filed|${r.year}|${r.crop}|${r.county}|${r.cropType||''}`;

      if (!byKey.has(key)) {
        byKey.set(key, {
          kind: r.kind,
          year: r.year,
          policyId: r.policyId || r.policy_id || null,
          crop: r.crop,
          cropType: r.cropType,
          county: r.county,
          cause: r.cause,
          claimType: r.claimType,
          claimTypeRaw: r.claimTypeRaw,
          claimNumber: r.claimNumber,
          claimTotal: r.claimTotal,
          adjuster: r.adjuster,
          aip: r.aip,
          productLabel: r.productLabel,
          quarter: r.quarter || null,   // DRP coverage quarter
          paidAmount: 0,
          pendingAmount: 0,
          reserveAmount: 0,           // P20A loss_code F/M that has NOT settled — not money owed yet
          creditAmount: 0,            // the premium-credit share of paidAmount (F/M), for the method caption
          paidCount: 0,
          pendingCount: 0,
          reserveCount: 0,
          creditCount: 0,
          lastCheckDate: null,
          firstCheckDate: null,
          disbursements: [],
        });
      }
      const g = byKey.get(key);
      g.disbursements.push(r);
      if (r.amount > 0) {
        const isCredit = r.lossCode === 'F' || r.lossCode === 'M';
        // A PREMIUM CREDIT IS SETTLED MONEY, dated or not. R&H ships 30 of them
        // ($240,397 across 19 claims) with status Paid and no check_date, and
        // treating a dateless one as an un-owed "reserve" made this card
        // disagree with the Claims page on the same claim — Voelpel's $62,297
        // soybean credit vanished from the paid headline here while the Claims
        // page counted it. The sync only marks M/F Paid once the claim settles
        // (isSettledCode), and it was verified against the R&H claim register,
        // so Paid is the signal to trust; the reserve bucket now catches only
        // an F/M that has NOT settled.
        if (r.checkDate || (isCredit && r.status === 'Paid')) {
          g.paidAmount += r.amount;
          g.paidCount++;
          if (isCredit) { g.creditAmount += r.amount; g.creditCount++; }
          if (r.checkDate) {
            if (!g.lastCheckDate || r.checkDate > g.lastCheckDate) g.lastCheckDate = r.checkDate;
            if (!g.firstCheckDate || r.checkDate < g.firstCheckDate) g.firstCheckDate = r.checkDate;
          }
        } else if (isCredit) {
          g.reserveAmount += r.amount;
          g.reserveCount++;
        } else {
          g.pendingAmount += r.amount;
          g.pendingCount++;
        }
      }
    }

    const groups = Array.from(byKey.values());
    // Summary status decision tree:
    //   • In Adjustment — every disbursement row is "Open" (P20X-only,
    //     no P20 yet — claim filed, adjuster assigned, no checks)
    //   • Paid — settled money present (E/L check or F/M premium credit) and
    //     nothing still pending
    //   • Open — some settled, some still pending
    //   • Pending — nothing settled yet
    //   • Filed — production_history-only, P15A flagged claim
    for (const g of groups) {
      const allOpen = g.disbursements.length > 0
        && g.disbursements.every(d => d.status === 'Open');
      if (g.kind === 'filed') g.summaryStatus = 'Filed';
      else if (allOpen) g.summaryStatus = 'In Adjustment';
      else if (g.pendingCount === 0 && g.paidCount > 0) g.summaryStatus = 'Paid';
      else if (g.paidCount === 0 && g.pendingCount > 0) g.summaryStatus = 'Pending';
      else if (g.paidCount > 0) g.summaryStatus = 'Open';
      else g.summaryStatus = 'No activity';
    }

    // Sort: newest year first, paid-or-paid-on-newer-date before pending,
    // descending lastCheckDate within year
    groups.sort((a, b) => {
      const yd = (b.year || 0) - (a.year || 0);
      if (yd) return yd;
      const aHas = a.lastCheckDate ? 1 : 0, bHas = b.lastCheckDate ? 1 : 0;
      if (aHas !== bHas) return bHas - aHas;
      return (b.lastCheckDate || '').localeCompare(a.lastCheckDate || '');
    });
    return groups;
  }, [taggedRows]);

  // Year-by-year summary at top — totals split into paid + pending, with a
  // crop+stage breakdown. Two-tier classification so simple claims are
  // displayed cleanly without being mixed in with messy multi-crop ones:
  //
  //   tier 0 — single crop, single stage  → "CORN · Prevent Plant"
  //   tier 1 — single crop, mixed stages  → "CORN · Mixed stages"
  //   tier 2 — multi-crop                 → "Mixed claim (CORN, DRY BEANS)"
  //
  // M13 doesn't tell us how a mixed claim's dollars split across its
  // crop/stage components, so we don't try — tier 2 is one line per
  // claim with the full claim total. Tiers sort 0 → 1 → 2 in display so
  // the clean attribution sits at the top.
  const breakdownKeyFor = (g) => {
    const crops  = (g.crop || '').split(',').map(s => s.trim()).filter(Boolean);
    const stages = (g.claimType || '').split(' + ').map(s => s.trim()).filter(Boolean);
    if (crops.length <= 1 && stages.length <= 1) {
      return { key: g.crop ? `${g.crop} · ${g.claimType}` : (g.claimType || 'Claim'), tier: 0 };
    }
    if (crops.length <= 1 && stages.length > 1) {
      return { key: `${g.crop || 'Crop'} · Mixed stages`, tier: 1, stages };
    }
    return { key: `Mixed claim (${crops.join(', ')})`, tier: 2, crops, stages };
  };

  // Apply productFilter at the claimGroups level so year
  // summaries + visible table both reflect the filtered set.
  // (Declared BEFORE yearSummary because yearSummary references it — moving
  // this below yearSummary triggers a Temporal Dead Zone error in the
  // production minified bundle.)
  //
  // Year cutoff: M13 only retains ~2 crop years on the AIP SFTP. Older
  // P15A M-flag rows persist in our DB forever (the AIP keeps the flag
  // but discards the P20/P20A claim records), so a 2022 WHEAT M-flag
  // shows here as "Stage TBD" with no payment data — misleading because
  // the claim DID pay out, we just can't fetch the records anymore.
  // CY2025 is the earliest year M13 can authoritatively reconstruct.
  const CLAIMS_MIN_YEAR = 2025;
  const filteredGroups = useMemo(() => {
    // CY2025 floor applies to DRP too (now by coverage-quarter year): we only
    // retain ~2 crop years of M13, so a pre-2025 coverage year is incomplete
    // (e.g. only Q4 2024, missing Q1–Q3) and would mislead. Dropping it here
    // keeps the year cards reconciled with the CY2025+ paid / loss-ratio totals.
    let out = claimGroups.filter(g => (g.year || 0) >= CLAIMS_MIN_YEAR);
    if (productFilter) {
      const target = productFilter.toLowerCase();
      out = out.filter(g => (g.productLabel || '').toLowerCase() === target);
    }
    // ⚠ NO SEASON NARROWING. This used to hide fall-crop claims on the Spring tab
    // and spring-crop claims on the Fall tab, which is why Fire Creek's PAID CY2026
    // WHEAT claim (MP-0926271, Livingston, $3,111) was absent from a card whose
    // header read "all-time paid: $20,393 · 3 paid claims".
    //
    // MEASURED BOOK-WIDE 2026-10-01, over every crop indemnity row:
    //     spring-only crops   1,048 rows   $23,905,226
    //     FALL-only crops       166 rows      $830,971   invisible from the Spring tab, 72 farms
    //     spans both seasons      0 rows           $0
    // The rule it replaced justified itself on multi-crop claims showing their full
    // dollars on both tabs — and THAT CASE DOES NOT EXIST IN THIS BOOK. It hid
    // $831K across 72 farms and prevented nothing.
    //
    // The deeper reason it was wrong: the season tabs scope COVERAGE, and a claims
    // history is not coverage — it is a farmer-level fact, loaded farmer-wide on
    // purpose (see the fetch above, which deliberately widens to every policy the
    // farmer holds). Narrowing it afterwards also made the headline figures lie:
    // "paid", "loss ratio" and "all-time paid" read as farm totals while counting
    // one season. The crop is on every row, so a fall claim on the spring page is
    // legible, not confusing — and money is the last thing a page should hide.
    return out;
  }, [claimGroups, productFilter]);

  // ── Decompose claim groups into per-(crop, county, stage) line rows ──
  // Uses M13 P21 stage-attributed disbursement data (source='p21' in
  // indemnity_lines) when available — that's the only way to split a
  // multi-stage claim's dollars by stage. Falls back to a single row per
  // claim group when P21 data isn't ingested yet (e.g. CY2025 historical
  // claims that pre-date this parser change).
  //
  // Crop/county fallback: claim rows from older backfills sometimes have
  // crop=null and county=null (the original buildIndemnityRows didn't
  // resolve these consistently). Use production_history M-flagged rows
  // for the same (policy, year) as a hint — those are the same yields
  // that drive the APH display, so they're the same crops.
  const phByPolicyYear = useMemo(() => {
    const m = {};
    for (const r of (prodRows || [])) {
      const isClaim = (r.production_status === 'M') ||
                      (r.record_type === 'PP' || r.record_type === 'PW' || r.record_type === 'PA');
      if (!isClaim) continue;
      if (!r.policy_id || !r.crop_year || !r.crop) continue;
      const key = `${r.policy_id}|${r.crop_year}`;
      if (!m[key]) m[key] = { crops: new Set(), counties: new Set() };
      m[key].crops.add(r.crop);
      if (r.county) m[key].counties.add(r.county);
    }
    return m;
  }, [prodRows]);

  const claimLineRows = useMemo(() => {
    const out = [];
    // Index P21 lines by (policy_id, crop_year, claim_number). The year is
    // load-bearing: AIP-issued claim numbers are unique per crop year but
    // the same number CAN repeat across years, and without the year filter
    // a 2026 line could merge into a 2025 claim row (or vice versa).
    const p21ByKey = {};
    for (const ln of (lineRows || [])) {
      if (ln.source !== 'p21') continue;
      const k = `${ln.policy_id}|${ln.crop_year || ''}|${ln.loss_key_base || ''}`;
      if (!p21ByKey[k]) p21ByKey[k] = [];
      p21ByKey[k].push(ln);
    }

    // P21 lines are keyed by CLAIM NUMBER on both AIPs, which is coarser than a
    // claim group now that groups key on loss_key. When one number covers two
    // claims (NAU does this — see claimGroups), the same stage lines match both
    // groups and would be counted twice. Attribution isn't in the file, so those
    // claims fall back to the legacy per-claim row: exact dollars, no stage
    // split, rather than a stage split that double-counts.
    const groupsPerClaimNo = {};
    for (const g of filteredGroups) {
      if (!g.policyId || !g.claimNumber) continue;
      const k = `${g.policyId}|${g.year || ''}|${g.claimNumber}`;
      groupsPerClaimNo[k] = (groupsPerClaimNo[k] || 0) + 1;
    }

    for (const g of filteredGroups) {
      // Only payment claims with a real claim number can join to P21.
      // Filed claims and claims without P21 fall through to the legacy
      // per-claim row.
      const key = g.policyId && g.claimNumber ? `${g.policyId}|${g.year || ''}|${g.claimNumber}` : null;
      const p21Lines = (key && groupsPerClaimNo[key] === 1) ? (p21ByKey[key] || []) : [];
      // How this claim's money moved — same classifier the Claims page uses.
      const payMix = claimPayMix(g.disbursements);

      if (p21Lines.length > 0) {
        // Aggregate by (crop, county, stage_group). One output row per cell.
        // stage_group normalizes companion codes (UH → H) so the production-
        // to-count offset nets against the gross harvest indemnity, matching
        // what the AIP actually paid for that (crop, county).
        const m = {};
        for (const ln of p21Lines) {
          const stageRaw = ln.stage_code || '';
          const stageKey = CLAIM_STAGE_GROUP[stageRaw] || stageRaw;
          const k = `${(ln.crop||'—').toUpperCase()}|${(ln.county||'—').toUpperCase()}|${stageKey}`;
          if (!m[k]) m[k] = {
            crop: ln.crop, county: ln.county, stageRaw: stageKey,
            paid: 0, count: 0, acres: 0,
          };
          m[k].paid += Number(ln.indemnity_actual) || 0;
          m[k].acres += Number(ln.acres) || 0;      // loss acres per stage (PP/Harvest/Replant)
          m[k].count++;
        }
        for (const v of Object.values(m)) {
          // P21 stage codes only carry crop-loss meaning on MPCI crop
          // policies. On PRF (and any non-crop product) they're index-payout
          // artifacts — e.g. FL here is the PRF rainfall payout, NOT the
          // ECO/SCO area-plan loss it denotes on crop rows — so show the
          // product type instead of mislabeling the stage. Mirrors the legacy
          // path (claimType = productLabel for LRP/DRP/PRF) and the farmer-
          // card guard (formatStageParts suppressed when policyType !== crop).
          const stageLabel = g.productLabel === 'Crop'
            ? (stageLabelFor(v.stageRaw, g.aip) || '—')
            : g.productLabel;
          out.push({
            kind: 'line',
            year: g.year,
            crop: v.crop || '—',
            county: v.county || '—',
            stage: stageLabel,
            stageRaw: stageLabel,                    // for tone color lookup
            paid: v.paid,
            pending: 0,
            reserve: 0,
            acres: v.acres > 0 ? v.acres : null,
            lineCount: v.count,
            claimNumber: g.claimNumber,
            aip: g.aip,
            productLabel: g.productLabel,
            policyId: g.policyId,
            source: 'p21',
            claimStatus: g.summaryStatus,
            // Method NAMES only: the dollar split is claim-level, and M13 gives
            // no per-cell disbursement to split it by. Naming the methods is
            // true of every line of the claim; assigning dollars would not be.
            payLabel: payMix.names,
            payTitle: payMix.title,
            // Reference back to the parent group for any drill-down logic
            _group: g,
          });
        }
      } else {
        // Legacy single-row-per-claim rendering (filed claims + claims
        // without P21 data). Stage column shows the claim's combined
        // stage labels or "Stage TBD". Per-(stage) split only happens
        // when P21 line items exist for the claim — never invent a
        // split when M13 doesn't publish one.
        // Crop/county fallback: when the indemnity row has crop=null
        // (older backfills), look at production_history M-flag rows for
        // the same policy+year to fill in. Same data that drives APH.
        let cropDisplay = g.crop;
        let countyDisplay = g.county;
        if ((!cropDisplay || !countyDisplay) && g.policyId && g.year) {
          const ph = phByPolicyYear[`${g.policyId}|${g.year}`];
          if (ph) {
            if (!cropDisplay && ph.crops.size > 0) {
              cropDisplay = Array.from(ph.crops).sort().join(', ');
            }
            if (!countyDisplay && ph.counties.size > 0) {
              countyDisplay = Array.from(ph.counties).sort().join(', ');
            }
          }
        }
        // DRP indemnity rows carry no county (cid is policy-level) — fall back
        // to the policy's county so the row isn't blank.
        if (!countyDisplay && g.policyId && policyCounty[g.policyId]) {
          countyDisplay = policyCounty[g.policyId];
        }
        out.push({
          kind: g.kind === 'filed' ? 'filed' : 'line',
          year: g.year,
          crop: cropDisplay || '—',
          county: countyDisplay || '—',
          stage: g.claimType,
          stageRaw: g.claimTypeRaw,
          cause: g.cause || null,            // P58 #23 (R&H) / P21 #21,24,27 (NAU) — see M13_DAMAGE_CAUSE
          quarter: g.quarter || null,        // DRP coverage quarter (e.g. "Q1 2026")
          checkDate: g.firstCheckDate || g.lastCheckDate || null,  // initial settlement date
          paid: g.paidAmount || 0,
          pending: g.pendingAmount || 0,
          reserve: g.reserveAmount || 0,
          acres: null,                       // no P21 stage detail (open / archived) → no acreage
          lineCount: 0,
          claimNumber: g.claimNumber,
          aip: g.aip,
          productLabel: g.productLabel,
          policyId: g.policyId,
          source: 'claim',
          claimStatus: g.summaryStatus,
          // One row = one whole claim here, so the dollar breakdown belongs.
          payLabel: payMix.label,
          payTitle: payMix.title,
          _group: g,
        });
      }
    }
    // Sort: newest year first. Within a year, order DRP lines chronologically
    // by coverage quarter (Q1→Q4) so they read sensibly; fall back to descending
    // paid amount (crop lines have no quarter, and ties within a quarter).
    out.sort((a, b) => {
      const yd = (b.year || 0) - (a.year || 0);
      if (yd) return yd;
      const qa = a.quarter ? drpSortKey(a.quarter) : null;
      const qb = b.quarter ? drpSortKey(b.quarter) : null;
      if (qa != null && qb != null && qa !== qb) return qa - qb;
      return (b.paid || 0) - (a.paid || 0);
    });
    return out;
  }, [filteredGroups, lineRows, phByPolicyYear, policyCounty]);

  const yearSummary = useMemo(() => {
    const byYear = {};
    for (const g of filteredGroups) {
      const yr = g.year;
      if (!yr) continue;
      if (!byYear[yr]) byYear[yr] = {
        year: yr,
        paidTotal: 0,           // settled money: E/L checks + F/M premium credits
        pendingTotal: 0,        // P20A 'P' loss code (claim filed, no check yet)
        reserveTotal: 0,        // F/M that has NOT settled — not money owed yet
        claimTotal: 0,          // P20 claim_total — the full awarded amount per claim
        claimCount: 0,
        paymentCount: 0,
        cropTotals: {},         // crop → claim_total share (summed from P21 lines)
      };
      byYear[yr].paidTotal    += g.paidAmount    || 0;
      byYear[yr].pendingTotal += g.pendingAmount || 0;
      byYear[yr].reserveTotal += g.reserveAmount || 0;
      // claim_total is per-claim — only count it once per group, not per disbursement
      byYear[yr].claimTotal   += g.claimTotal != null
        ? Number(g.claimTotal)
        : ((g.paidAmount || 0) + (g.pendingAmount || 0) + (g.reserveAmount || 0));
      byYear[yr].claimCount++;
      byYear[yr].paymentCount += g.paidCount + g.pendingCount;
    }
    // Per-crop totals come from claimLineRows (P21 attribution when
    // available, falls back to the claim's joined crop string for groups
    // that don't have P21 data yet). Each line is already per-(crop,
    // county, stage) — we sum across stages to get the per-crop total.
    for (const line of claimLineRows) {
      const yr = line.year;
      if (!yr || !byYear[yr]) continue;
      const cropKey = line.crop && line.crop !== '—' ? line.crop : 'Unattributed';
      // For "Mixed claim" rows (claim total fallback w/ multiple crops),
      // crop is "CORN, SOYBEANS" etc. Keep that label as-is so it's clear
      // which claims couldn't be split per-crop yet.
      if (!byYear[yr].cropTotals[cropKey]) byYear[yr].cropTotals[cropKey] = 0;
      byYear[yr].cropTotals[cropKey] += (line.paid || 0);
    }
    return Object.values(byYear)
      .map(y => {
        const cropRows = Object.entries(y.cropTotals)
          .map(([crop, amount]) => ({ crop, amount }))
          .filter(r => r.amount > 0)
          .sort((a, b) => b.amount - a.amount);
        return { ...y, total: y.paidTotal + y.pendingTotal, cropRows };
      })
      .sort((a, b) => b.year - a.year);
  }, [filteredGroups, claimLineRows]);

  // Year filter — default to "All Years"
  const [yearFilter, setYearFilter] = useState('all');
  const visibleLines = useMemo(() => {
    if (yearFilter === 'all') return claimLineRows;
    return claimLineRows.filter(line => String(line.year) === String(yearFilter));
  }, [claimLineRows, yearFilter]);

  // Group lines by year so we can render a subtotal header above each
  // year's rows. Map preserves insertion order — claimLineRows is already
  // sorted newest-first, so years appear newest-first in the output.
  const linesByYear = useMemo(() => {
    const m = new Map();
    for (const line of visibleLines) {
      const y = line.year || 0;
      if (!m.has(y)) m.set(y, []);
      m.get(y).push(line);
    }
    return Array.from(m.entries()).map(([year, lines]) => ({
      year,
      lines,
      paidTotal:    lines.reduce((s, l) => s + (l.paid || 0), 0),
      pendingTotal: lines.reduce((s, l) => s + (l.pending || 0), 0),
      reserveTotal: lines.reduce((s, l) => s + (l.reserve || 0), 0),
    }));
  }, [visibleLines]);

  if (loading) return <div style={{ textAlign:"center", padding:"40px 0", color:"#60735f" }}>Loading claims history...</div>;

  if (filteredGroups.length === 0) {
    const productNoun = productFilter ? productFilter.toUpperCase() : '';
    const emptyMsg = productFilter
      ? `No ${productNoun} claims on record for this farmer.`
      : 'This farmer has no indemnity payments and no claim-flagged production records on file.';
    return (
      <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #c8d8c8" }}>
        <div style={{ fontSize:32, marginBottom:10 }}>📋</div>
        <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:6 }}>No claims on record</div>
        <div style={{ fontSize:13, color:"#60735f" }}>{emptyMsg}</div>
      </Card>
    );
  }

  const fmtMoney = (v) => v == null ? "—" : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      return new Date(s + (s.length === 10 ? "T12:00:00" : ""))
        .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return s; }
  };

  // Headline totals — paid is real disbursements, pending is "P20A entries
  // with no check date AND not loss_code F/M" (i.e. true unpaid claims),
  // paid now includes settled premium credits (F/M marked Paid) — they're
  // indemnity, just applied against premium instead of deposited. reserve is
  // only an F/M that hasn't settled, kept out of the pending headline.
  const grandPaid    = filteredGroups.reduce((s, g) => s + (g.paidAmount    || 0), 0);
  const grandPending = filteredGroups.reduce((s, g) => s + (g.pendingAmount || 0), 0);
  const grandReserve = filteredGroups.reduce((s, g) => s + (g.reserveAmount || 0), 0);
  const grandFiled   = filteredGroups.filter(g => g.kind === 'filed').length;

  // ── Loss ratio: lifetime premium (filtered to product + same year cutoff
  // as claim rows) vs paid claims. Premium needs to match the year window
  // we're showing claims for, otherwise the ratio is misleading.
  const filteredPremiums = premiumRows.filter(p => {
    if (productFilter && p.product !== productFilter) return false;
    if (p.crop_year && p.crop_year < CLAIMS_MIN_YEAR) return false;
    return true;
  });
  const grandPremium = filteredPremiums.reduce((s, p) => s + (p.premium || 0), 0);
  const lifetimeRatio = grandPremium > 0 ? grandPaid / grandPremium : null;

  // (Removed: a linesByClaim index keyed on loss_key_base alone. It was unused,
  // and the key is unsafe — 36 loss_key_base values span more than one
  // (policy, crop year) across 1,424 line rows, because AIP claim numbers only
  // reset per crop year. Anything indexing these lines must key on
  // policy + crop_year + loss_key_base, the way claimLineRows does.)

  // Color the loss-ratio number by severity. <50% = subsidy-positive (good
  // book performance); 50-100% = neutral; 100-200% = elevated; >200% = high
  // loss farmer.
  const ratioColor = (r) => {
    if (r == null) return "#60735f";
    if (r < 0.5)  return "#2d5a10";
    if (r < 1)    return "#4a7a2a";
    if (r < 2)    return "#a06020";
    return "#a02020";
  };
  const fmtRatio = (r) => r == null ? "—" : `${(r * 100).toFixed(0)}%`;

  return (
    <div>
      {/* ── Lifetime loss-ratio summary ── */}
      {/* Premium-paid context for the indemnities below. When productFilter
          is set this scopes to that product; when unset it shows the full
          book. Only rendered when we have any premium data — otherwise no
          ratio can be computed. */}
      {grandPremium > 0 && (
        <div style={{ marginBottom:14, display:"flex", gap:10, flexWrap:"wrap" }}>
          <div style={{ flex:"1 1 200px", padding:"12px 16px", border:"1px solid #e0e8e0", borderRadius:10, background:"#fff" }}>
            <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#60735f", textTransform:"uppercase", marginBottom:6 }}>Premium · CY{CLAIMS_MIN_YEAR}+</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(grandPremium)}</div>
            <div style={{ fontSize:10, color:"#60735f", marginTop:2 }}>{productFilter ? `${productFilter.toUpperCase()} only` : 'all products'} · farmer-paid</div>
          </div>
          <div style={{ flex:"1 1 200px", padding:"12px 16px", border:"1px solid #e0e8e0", borderRadius:10, background:"#fff" }}>
            <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#60735f", textTransform:"uppercase", marginBottom:6 }}>Paid · CY{CLAIMS_MIN_YEAR}+</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#2d5a10", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(grandPaid)}</div>
            <div style={{ fontSize:10, color:"#60735f", marginTop:2 }}>{filteredGroups.filter(g => g.paidAmount > 0).length} paid claims</div>
          </div>
          <div style={{ flex:"1 1 200px", padding:"12px 16px", border:`2px solid ${ratioColor(lifetimeRatio)}33`, borderRadius:10, background:"#fff" }}>
            <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#60735f", textTransform:"uppercase", marginBottom:6 }}>Loss Ratio</div>
            <div style={{ fontSize:22, fontWeight:800, color: ratioColor(lifetimeRatio), fontVariantNumeric: "tabular-nums" }}>{fmtRatio(lifetimeRatio)}</div>
            <div style={{ fontSize:10, color:"#60735f", marginTop:2 }}>paid ÷ premium · CY{CLAIMS_MIN_YEAR}+</div>
          </div>
        </div>
      )}

      {/* Year-by-year summary cards — quick view of "how much paid in YYYY"
          with breakdown by crop and Prevent-Plant vs Harvest split.
          Each card is clickable to filter the table to that year. */}
      <div style={{ marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <div style={{ fontSize:12, fontWeight:800, color:"#1a2e1a", textTransform:"uppercase", letterSpacing:"0.08em" }}>
            💰 Indemnities by Year
            <span style={{ fontSize:10, fontWeight:500, color:"#60735f", marginLeft:8, textTransform:"none", letterSpacing:0 }}>
              all-time paid: <strong style={{ color:"#2d5a10" }}>{fmtMoney(grandPaid)}</strong>
              {grandPending > 0 && <span style={{ marginLeft:6, color:"#a06020" }}>· pending: <strong>{fmtMoney(grandPending)}</strong></span>}
              {grandReserve > 0 && (
                <span style={{ marginLeft:6, color:"#9a8270" }} title="P20A loss code F/M that has not settled — an AIP ledger entry, not money owed to the farmer. A SETTLED F/M is a premium credit and counts as paid.">
                  · {fmtMoney(grandReserve)} reserve <span style={{ fontSize:10 }}>(?)</span>
                </span>
              )}
              {grandFiled > 0 && <span style={{ marginLeft:6, color:"#7a4a00" }}>· {grandFiled} filed (no payment yet)</span>}
            </span>
          </div>
          {yearSummary.length > 1 && (
            <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
              style={{ padding:"4px 10px", border:"1px solid #d0e0d0", borderRadius:6, fontSize:12, fontWeight:700, color:"#2d4a2d", background:"#fff" }}>
              <option value="all">All years</option>
              {yearSummary.map(y => <option key={y.year} value={y.year}>{y.year}</option>)}
            </select>
          )}
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          {yearSummary.map(y => {
            const isActive = String(yearFilter) === String(y.year);
            const hasMoney = y.total > 0;
            return (
              <button key={y.year}
                onClick={() => setYearFilter(isActive ? 'all' : String(y.year))}
                style={{
                  flex: "0 1 280px",
                  padding: "12px 14px",
                  background: isActive ? "#f4fbf2" : "#fff",
                  border: `2px solid ${isActive ? "#2d5a10" : hasMoney ? "#4a7a2a44" : "#e0e8e0"}`,
                  borderRadius: 10,
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
                  <div style={{ fontSize:18, fontWeight:800, color:"#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{y.year}</div>
                  {paymentYearMode
                    ? <div style={{ fontSize:18, fontWeight:800, color: y.paidTotal > 0 ? "#2d5a10" : "#aaa", fontVariantNumeric: "tabular-nums" }} title="Indemnity actually paid in this calendar year (sum of checks dated Jan–Dec).">{fmtMoney(y.paidTotal)}</div>
                    : <div style={{ fontSize:18, fontWeight:800, color: y.claimTotal > 0 ? "#2d5a10" : "#aaa", fontVariantNumeric: "tabular-nums" }} title="Total claim amount (P20 claim_total) — what the AIP has awarded for this year, regardless of how much has cleared as cash">{fmtMoney(y.claimTotal)}</div>}
                </div>
                <div style={{ fontSize:10, color:"#60735f", marginBottom:6, display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:4 }}>
                  <span>{y.claimCount} claim{y.claimCount === 1 ? "" : "s"} · {y.paymentCount} payment{y.paymentCount === 1 ? "" : "s"}</span>
                </div>
                {/* ⚠ A YEAR OF CLAIMS WITH NO DOLLARS IS A GAP IN OUR RECORDS, NOT A YEAR
                    THE GROWER WAS PAID NOTHING, and the card said neither. Fire Creek
                    Farms reads "2025 · 4 claims · 0 payments · $0" and Kevin's question
                    was the right one: they HAD 2025 claims. The claim count on these
                    cards comes from the carrier's own APH (a P15A claim flag); the
                    dollars come from P20/P20A, which M13 only carries for about two crop
                    years. Measured 2026-10-01: 18 farms are in exactly this state, and
                    every one of them has NO policy row for that crop year at all, while
                    only 2 of the 194 farms whose CY2025 claims DID load have one — so a
                    missing year is not what drops the dollars; a missing policy to hang
                    them on is, which is what a carrier change leaves behind.
                    Stated as the likeliest reading, not as a finding about this farm. */}
                {y.claimCount > 0 && y.claimTotal === 0 && y.paymentCount === 0 && (
                  <div style={{ fontSize:10.5, color:"#8a6d1f", marginBottom:6, lineHeight:1.5 }}>
                    Flagged on the carrier's own APH for this year, with no payment records on file.
                    M13 carries roughly two crop years, so a claim written under a policy that is no
                    longer in the file — typically after a carrier change — arrives as a flag without
                    dollars. The amount is unknown here, not zero.
                  </div>
                )}
                <div style={{ fontSize:10, color:"#60735f", marginBottom:4, display:"flex", justifyContent:"space-between", gap:4, flexWrap:"wrap" }}>
                  {y.paidTotal > 0    && <span style={{ color:"#2d5a10" }}>{fmtMoney(y.paidTotal)} paid</span>}
                  {y.pendingTotal > 0 && <span style={{ color:"#a06020" }}>{fmtMoney(y.pendingTotal)} pending</span>}
                  {y.reserveTotal > 0 && <span style={{ color:"#9a8270" }} title="P20A loss code F/M that has not settled — an AIP ledger entry, not an amount owed. A settled F/M is a premium credit and counts as paid.">{fmtMoney(y.reserveTotal)} reserve <span style={{ fontSize:10 }}>(?)</span></span>}
                </div>
                {!paymentYearMode && y.cropRows && y.cropRows.length > 0 && (
                  <div style={{ display:"flex", flexDirection:"column", gap:3, marginTop:6, paddingTop:6, borderTop:"1px solid #eef2ee" }}>
                    <div style={{ fontSize:10, fontWeight:700, color:"#60735f", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:2 }}>
                      Total claims by crop
                    </div>
                    {y.cropRows.map((row) => (
                      <div key={row.crop} style={{ display:"flex", justifyContent:"space-between", fontSize:11, alignItems:"baseline", gap:8 }}>
                        <span style={{ color:"#1a2e1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {row.crop}
                        </span>
                        <span style={{ fontVariantNumeric: "tabular-nums", color: row.amount > 0 ? "#2d5a10" : "#aaa", flexShrink:0 }}>{fmtMoney(row.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Flat claims table — one row per (year, crop, county, stage). Year
          subtotal row inserted at the top of each year's group. P21 stage-
          attributed disbursement lines drive the per-stage split when
          available; falls back to one row per claim group otherwise.
          Mobile: cards grouped by year so the 6-col table doesn't horizontal-scroll. */}
      {isMobile ? (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {linesByYear.length === 0 && (
            <div style={{ padding:"24px", textAlign:"center", color:"#60735f", fontStyle:"italic", fontSize:13 }}>
              No claim activity matches the current filter.
            </div>
          )}
          {linesByYear.map((yearGroup, yi) => {
            const claimTypeColors = {
              'Prevent Plant': { bg:"#fef4e8", fg:"#7a4a00" },
              'Harvest Loss':  { bg:"#eef4ff", fg:"#1a4a8a" },
              'Replant':       { bg:"#f4ecff", fg:"#5a2a8a" },
              'LRP':           { bg:"#f0f6f0", fg:"#2d5a10" },
              'DRP':           { bg:"#f0f6f0", fg:"#2d5a10" },
              'PRF':           { bg:"#f0f6f0", fg:"#2d5a10" },
              'Crop Loss':     { bg:"#eef4ff", fg:"#1a4a8a" },
              'Filed':         { bg:"#fef8ed", fg:"#7a4a00" },
            };
            return (
              <React.Fragment key={yearGroup.year}>
                <div style={{ background:"#1a2e1a", color:"#fff", padding:"10px 14px", borderRadius:8, fontSize:12, fontWeight:800, letterSpacing:"0.06em" }}>
                  <span style={{ fontVariantNumeric: "tabular-nums", marginRight:12 }}>{yearGroup.year}</span>
                  <span style={{ fontWeight:500, color:"#a8c8a8" }}>
                    {fmtMoney(yearGroup.paidTotal)} indemnity
                    <span style={{ marginLeft:6 }}>· {yearGroup.lines.length} {yearGroup.lines.length === 1 ? 'line' : 'lines'}</span>
                  </span>
                </div>
                {yearGroup.lines.map((line, li) => {
                  const tone = claimTypeColors[line.stageRaw] || claimTypeColors[line.stage] || { bg:"#f4f4f4", fg:"#5a5a5a" };
                  const isOpenClaim = line.claimStatus === 'In Adjustment' || line.claimStatus === 'Open';
                  const status = line.kind === 'filed' ? 'Filed' :
                                 line.paid > 0 ? 'Paid' :
                                 line.pending > 0 ? 'Pending' :
                                 isOpenClaim ? 'Open' : '—';
                  const statusBg = status === 'Paid' ? '#eaf4e4'
                                  : status === 'Filed' ? '#fef8ed'
                                  : status === 'Pending' ? '#fef4e8'
                                  : status === 'Open' ? '#fdf1e8' : '#f4f4f4';
                  const statusFg = status === 'Paid' ? '#2d5a10'
                                  : status === 'Filed' ? '#7a4a00'
                                  : status === 'Pending' ? '#a06020'
                                  : status === 'Open' ? '#b34a10' : '#5a5a5a';
                  return (
                    <div key={`${yi}-${li}`} style={{ background:"#fff", border:"1px solid #e4ece4", borderRadius:10, padding:"12px 14px", marginLeft:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6, gap:8 }}>
                        <span style={{ fontWeight:700, fontSize:14, color: line.crop === '—' ? "#bbb" : "#1a2e1a" }}>{line.crop}</span>
                        <span style={{ textAlign:"right", whiteSpace:"nowrap" }} title={line.payTitle || undefined}>
                          <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:14, color: line.paid > 0 ? "#2d5a10" : "#bbb" }}>
                            {line.paid > 0 ? fmtMoney(line.paid) : "—"}
                          </span>
                          {line.paid > 0 && line.payLabel && (
                            <span style={{ display:"block", fontSize:10, fontWeight:700, color:"#7a5b00", textTransform:"uppercase", letterSpacing:"0.04em" }}>{line.payLabel}</span>
                          )}
                        </span>
                      </div>
                      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:6 }}>
                        <span style={{ fontSize:11, color: line.county === '—' ? "#bbb" : "#5a7a5a" }}>📍 {line.county}</span>
                        <span style={{ background:tone.bg, color:tone.fg, border:`1px solid ${tone.fg}33`, borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{line.stage}</span>
                        {line.quarter && <span style={{ fontSize:11, fontWeight:700, color:"#3a5a8a" }}>{line.quarter}</span>}
                        {line.acres != null && <span style={{ fontSize:11, color:"#5a7a5a", fontVariantNumeric: "tabular-nums" }}>{fmt(line.acres,1)} ac</span>}
                        {line.cause && (
                          <span style={{ fontSize:11, color:"#7a5b3a", background:"#faf4ec", border:"1px solid #e5d5c0",
                                         borderRadius:4, padding:"2px 8px", fontWeight:600 }}
                                title={"Cause of loss as the AIP filed it — P58 Notice of Loss field 23 (Rain & Hail) or P21 fields 21/24/27, the paid lines (NAU). "
                                     + "Code first, label from RMA's own ICE D00075 damage-cause list (RY2026), read verbatim from RMA's public reference directory."}>
                            {line.cause}
                          </span>
                        )}
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, fontSize:11 }}>
                        <span style={{
                          padding:"2px 8px", borderRadius:4, fontWeight:700,
                          background: statusBg, color: statusFg,
                          border: `1px solid ${statusFg}33`,
                        }}>{status}</span>
                        <span style={{ color:"#9aa595", fontSize:10, textAlign:"right" }}>
                          {line.source === 'p21' ? 'P21 attributed' : line.source === 'claim' ? 'Claim total' : '—'}
                          {line.lineCount > 1 && ` · ${line.lineCount} lines`}
                          {line.checkDate && ` · paid ${line.checkDate}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
      <div style={{ border:"1px solid #e4ece4", borderRadius:10, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ background:"#f4fbf2" }}>
              <th style={{ padding:"8px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#5a7a5a", textTransform:"uppercase", letterSpacing:"0.06em" }}>County</th>
              <th style={{ padding:"8px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#5a7a5a", textTransform:"uppercase", letterSpacing:"0.06em" }}>Crop</th>
              <th style={{ padding:"8px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#5a7a5a", textTransform:"uppercase", letterSpacing:"0.06em" }} title="Stage code from M13 P21 f[15]/f[16]: Prevent Plant, Harvest Loss, Replant. Filed claims with no stage data show 'Stage TBD'.">Stage / Type</th>
              <th style={{ padding:"8px 14px", textAlign:"right", fontSize:11, fontWeight:700, color:"#5a7a5a", textTransform:"uppercase", letterSpacing:"0.06em" }} title="Loss acres for this stage from M13 P21 f[17] — e.g. prevent-plant acres, replant acres, harvest-loss acres.">Acres</th>
              <th style={{ padding:"8px 14px", textAlign:"right", fontSize:11, fontWeight:700, color:"#5a7a5a", textTransform:"uppercase", letterSpacing:"0.06em" }} title="Total indemnity amount per (crop, county, stage) — what the AIP has awarded. May exceed cash actually disbursed (some stages may still be in pending/reserve disposition at the claim level).">Indemnity</th>
              <th style={{ padding:"8px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#5a7a5a", textTransform:"uppercase", letterSpacing:"0.06em" }}>Source</th>
              <th style={{ padding:"8px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#5a7a5a", textTransform:"uppercase", letterSpacing:"0.06em" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {linesByYear.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding:"24px", textAlign:"center", color:"#60735f", fontStyle:"italic" }}>
                  No claim activity matches the current filter.
                </td>
              </tr>
            )}
            {linesByYear.map((yearGroup, yi) => {
              const claimTypeColors = {
                'Prevent Plant': { bg:"#fef4e8", fg:"#7a4a00" },
                'Harvest Loss':  { bg:"#eef4ff", fg:"#1a4a8a" },
                'Replant':       { bg:"#f4ecff", fg:"#5a2a8a" },
                'LRP':           { bg:"#f0f6f0", fg:"#2d5a10" },
                'DRP':           { bg:"#f0f6f0", fg:"#2d5a10" },
                'PRF':           { bg:"#f0f6f0", fg:"#2d5a10" },
                'Crop Loss':     { bg:"#eef4ff", fg:"#1a4a8a" },
                'Filed':         { bg:"#fef8ed", fg:"#7a4a00" },
              };
              return (
                <React.Fragment key={yearGroup.year}>
                  {/* Year subtotal header row */}
                  <tr style={{ background:"#1a2e1a" }}>
                    <td colSpan={7} style={{ padding:"10px 14px", color:"#fff", fontSize:12, fontWeight:800, letterSpacing:"0.06em" }}>
                      <span style={{ fontVariantNumeric: "tabular-nums", marginRight:14 }}>{yearGroup.year}</span>
                      <span style={{ fontWeight:500, color:"#a8c8a8" }}>
                        {fmtMoney(yearGroup.paidTotal)} indemnity
                        <span style={{ marginLeft:8 }}>· {yearGroup.lines.length} {yearGroup.lines.length === 1 ? 'line' : 'lines'}</span>
                      </span>
                    </td>
                  </tr>
                  {/* Lines for this year */}
                  {yearGroup.lines.map((line, li) => {
                    const tone = claimTypeColors[line.stageRaw] || claimTypeColors[line.stage] || { bg:"#f4f4f4", fg:"#5a5a5a" };
                    const isOpenClaim = line.claimStatus === 'In Adjustment' || line.claimStatus === 'Open';
                    const status = line.kind === 'filed' ? 'Filed' :
                                   line.paid > 0 ? 'Paid' :
                                   line.pending > 0 ? 'Pending' :
                                   isOpenClaim ? 'Open' : '—';
                    const statusBg = status === 'Paid' ? '#eaf4e4'
                                    : status === 'Filed' ? '#fef8ed'
                                    : status === 'Pending' ? '#fef4e8'
                                    : status === 'Open' ? '#fdf1e8' : '#f4f4f4';
                    const statusFg = status === 'Paid' ? '#2d5a10'
                                    : status === 'Filed' ? '#7a4a00'
                                    : status === 'Pending' ? '#a06020'
                                    : status === 'Open' ? '#b34a10' : '#5a5a5a';
                    return (
                      <tr key={`${yi}-${li}`} style={{
                        borderTop: "1px solid #f0f4f0",
                        background: li%2===0 ? "#fff" : "#fafcfa",
                      }}>
                        <td style={{ padding:"8px 14px", color: line.county === '—' ? "#bbb" : "#5a7a5a" }}>{line.county}</td>
                        <td style={{ padding:"8px 14px", color: line.crop === '—' ? "#bbb" : "#1a2e1a", fontWeight: line.crop === '—' ? 400 : 700 }}>{line.crop}</td>
                        <td style={{ padding:"8px 14px" }}>
                          <span style={{ background:tone.bg, color:tone.fg, border:`1px solid ${tone.fg}33`, borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{line.stage}</span>
                          {line.quarter && <span style={{ marginLeft:6, fontSize:11, fontWeight:700, color:"#3a5a8a" }}>{line.quarter}</span>}
                        </td>
                        <td style={{ padding:"8px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", color: line.acres != null ? "#5a7a5a" : "#ccc" }}>
                          {line.acres != null ? `${fmt(line.acres,1)} ac` : "—"}
                        </td>
                        <td style={{ padding:"8px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color: line.paid > 0 ? "#2d5a10" : "#bbb" }} title={line.payTitle || undefined}>
                          {line.paid > 0 ? fmtMoney(line.paid) : "—"}
                          {line.paid > 0 && line.payLabel && (
                            <div style={{ fontSize:10, fontWeight:700, color:"#7a5b00", fontFamily:"inherit", textTransform:"uppercase", letterSpacing:"0.04em" }}>{line.payLabel}</div>
                          )}
                          {line.lineCount > 1 && <div style={{ fontSize:10, fontWeight:500, color:"#60735f" }}>{line.lineCount} P21 lines</div>}
                        </td>
                        <td style={{ padding:"8px 14px", color:"#60735f", fontSize:10 }}>
                          {line.source === 'p21' ? <span title="Per-(crop, county, stage) split from M13 P21 stage-attributed disbursement records.">P21 attributed</span>
                            : line.source === 'claim' ? <span title="Single-row fallback: P21 stage data not yet ingested for this claim. Re-run backfill with current parser to get per-stage attribution.">Claim total</span>
                            : '—'}
                          {line.checkDate && <div style={{ fontSize:10, color:"#9aa8a0", marginTop:1 }}>paid {line.checkDate}</div>}
                        </td>
                        <td style={{ padding:"8px 14px", fontSize:11 }}>
                          <span style={{
                            padding:"2px 8px", borderRadius:4, fontWeight:700,
                            background: statusBg, color: statusFg,
                            border: `1px solid ${statusFg}33`,
                          }} title={line.claimNumber ? `Claim #${line.claimNumber}` : ""}>{status}</span>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      <div style={{ fontSize:11, color:"#60735f", marginTop:8, lineHeight:1.5 }}>
        <div style={{ marginBottom:6, fontStyle:"italic" }}>
          <strong>P21 attributed</strong> rows split a claim into authoritative per-(crop, county, stage) lines from M13 P21 records. <strong>Claim total</strong> rows are mixed-stage claims that haven't yet been resynced with the P21 ingest — they show as a single row with combined stages until the year's backfill is re-run (no estimation: M13 doesn't publish a per-stage dollar split, so we don't fake one). <strong>Reserve</strong> (in year subtotal) = P20A loss codes F/M with no check date; CLAUDE.md flags these as likely AIP reserve/escrow ledger entries, not amounts owed. <strong>Filed</strong> = P15A row flagged as a claim by the AIP with no P20 yet. <strong>Stage / Type</strong> labels from M13 stage codes (PF/H/R/REP/PP/HAR); unmapped codes render as raw.
        </div>
        <details style={{ fontSize:10, color:"#a08060", cursor:"pointer" }}>
          <summary style={{ outline:"none" }}>What's NOT in M13 (data limits)</summary>
          <ul style={{ margin:"6px 0 0 16px", padding:0 }}>
            <li>Mixed-claim dollar attribution — for a single claim that spans multiple cids/stages, P20A only carries the disbursement total, not the per-stage split. The per-cell breakdown below uses M13 P26 (per-unit indemnity calculation); P26 sums often differ slightly from P20A totals because P26 includes calculated values for line items that haven't yet been disbursed.</li>
            <li>Historical claims older than ~2 years — the AIPs only retain ~2 crop years on the SFTP. CY2023 and earlier paid checks are no longer in any M13 file we can fetch.</li>
            <li><strong>Cause of loss — CORRECTED 2026-08-19, it IS in M13.</strong> This note used to say the codes were unreliable because "most P12 records come back with cause 00". P12 is the <em>Payment</em> record and carries no cause of loss at all, so that field was the Payment Type Code. The real cause is on <strong>P58 (Notice of Loss)</strong> field 23 for Rain &amp; Hail and on <strong>P21</strong> fields 21/24/27 — the paid lines — for NAU, and it is populated on essentially every claim. Codes are shown raw with a label from RMA's M13 Exhibit 21-3; that exhibit is a 2018 revision and the current authority is ICE D00075, which we do not hold, so treat an unfamiliar label as unconfirmed.</li>
            <li>Loss codes F/M (small amount + large amount with no check date) may be reserve/escrow ledger entries; their precise meaning is not in our M13 spec yet — verify on the AIP statement before quoting.</li>
          </ul>
        </details>
      </div>
    </div>
  );
}

function SpringView({ farmer, prices, onChange, isMobile, actions, customerView = false }) {
  const [tab, setTab] = useState("revenue");
  const [procBeansPrice, setProcBeansPrice] = useState(() => farmer.metadata?.processingBeansPrice || "");

  // Prevent-plant acres per (crop, county) — AUTHORITATIVE from the AIP M13 feed
  // (crop_coverage.pp_acres, written from P11 f[37]='P' prevent-plant units). Used
  // to split the blended RMA liability so the Revenue Guarantee reflects PLANTED
  // acres only. NO heuristics — never infer PP from missing planting dates.
  const [ppByKey, setPpByKey] = useState({});
  // Uninsurable acres (crop_coverage.ui_acres) — acres carrying $0 liability ON
  // THIS CROP LINE, which the SOI counts outside its INSURED total. Already
  // excluded from `acres` by the sync, so they change no guarantee; carried here
  // so the card can show them and the page ties: total = insured + UI,
  // insured = planted + PP.
  //
  // These acres are NOT necessarily uncovered. Where the cause is prevent plant
  // beyond the crop's max eligible PP, the AIP ROLLS them onto another crop and
  // pays them there (Fire Creek 2026: 56.7 Ontario corn ac → 56.3 soybeans +
  // 0.4 dry beans, $12,612 + $73 of PP liability). The file gives no link
  // between the two sides, so nothing here infers it — see the label's tooltip.
  const [uiByKey, setUiByKey] = useState({});
  useEffect(() => {
    const pids = (farmer.allPolicyIds || farmer._allPolicyIds || []);
    if (!pids.length) { setPpByKey({}); return; }
    let alive = true;
    sbFetch("crop_coverage", { select: PP_UI_SELECT,
      filters: [`policy_id=in.(${pids.join(",")})`, PP_UI_FILTER] })
      .then(rows => {
        if (!alive) return;
        const { pp, ui } = buildPpUiMaps(rows);
        setPpByKey(pp); setUiByKey(ui);
      }).catch(() => {});
    return () => { alive = false; };
  }, [farmer.id, farmer.cropYear]);
  // Take the ROW, not (crop, county) — a crop's grain and silage lines, or its
  // conventional and organic lines, carry their own prevent plant separately.
  const ppFor = (row) => ppByKey[ppKeyOfRow(row)] || 0;
  const uiFor = (row) => uiByKey[ppKeyOfRow(row)] || 0;
  // Per-unit detail for the "N units" drill-down (crop_coverage_units).
  const unitsByKey = useCoverageUnits(farmer);
  const [openUnits, setOpenUnits] = useState(() => new Set());
  const toggleUnits = (k) => setOpenUnits(prev => { const x = new Set(prev); x.has(k) ? x.delete(k) : x.add(k); return x; });

  const _rawSpringYields = (farmer.approvedYields||[]).filter(a => !FALL_CROPS.includes(a.crop));

  async function saveProcBeansPrice(val) {
    setProcBeansPrice(val);
    const mergedMeta = { ...(farmer.metadata || {}), processingBeansPrice: val === "" ? null : parseFloat(val) };
    await fetch(`${SUPABASE_URL}/rest/v1/farmers?id=eq.${farmer.id}`, {
      method: "PATCH",
      headers: { ...getAuthHeaders(), Prefer: "return=minimal" },
      body: JSON.stringify({ metadata: mergedMeta })
    });
    if (onChange) onChange({ ...farmer, metadata: mergedMeta });
  }

  const procBeansPriceNum = parseFloat(procBeansPrice) || 0;
  // SCO/ECO for spring crops — loaded into scoEcoEndorsements by data layer, not approvedYields
  const scoEcoSpring = (farmer.scoEcoEndorsements||[]).filter(e => !FALL_CROPS.includes((e.crop||'').toUpperCase()));
  const seenSpring = new Set();
  const springYields = _rawSpringYields.filter(a => {
    const key = `${(a.county||"").toUpperCase().trim()}|${(a.crop||"").toUpperCase()}|${(a.type||"").toUpperCase()}|${a.plan}|${a.coverageLevel}|${normalizePractice(a.practice)}`;
    if (seenSpring.has(key)) return false;
    seenSpring.add(key); return true;
  });
  const hasPP         = (farmer.ppEligibility||[]).some(p => !FALL_CROPS.includes((p.crop||"").toUpperCase()));
  const hasAPH        = springYields.length > 0;

  // Revenue-guarantee acres = reported acres on the policy (crop_coverage.acres).
  // This is the SOI "Insured Acres" figure, which already includes reported prevent-plant
  // acres (PP is insured). PP *eligibility* (the pre-season worksheet) is NOT used here.

  const T = (active) => ({
    padding: isMobile ? "8px 10px" : "10px 22px", border:"none", background:"none", cursor:"pointer",
    borderBottom: active ? "3px solid #2d4a2d" : "3px solid transparent",
    color: active ? "#1a2e1a" : "#60735f",
    fontWeight: active ? 800 : 500, fontSize: isMobile ? 12 : 14, marginBottom:-1,
    whiteSpace:"nowrap",
  });

  return (
    <div>
      <APHBanner farmer={farmer} fallOnly={false} />

      {/* Tab bar — wraps to a second row on narrow screens so labels stay readable */}
      <div style={{ display:"flex", flexWrap:"wrap", borderBottom:"1px solid #e4ece4", marginBottom:20 }}>
        <button style={T(tab==="revenue")} onClick={()=>setTab("revenue")}>
          📋 {isMobile ? "Revenue" : "Revenue Guarantee"}
        </button>
        <button style={T(tab==="pp")} onClick={()=>setTab("pp")}>
          🌧️ {isMobile ? "PP" : "Prevent Plant"}
          {hasPP && <span style={{ marginLeft:6, fontSize:11, background:"#eef6ec", border:"1px solid #4a7a2a44", borderRadius:10, padding:"1px 7px", color:"#2d5a10", fontWeight:700 }}>{dedupPPRows(farmer.ppEligibility||[]).filter(p => !FALL_CROPS.includes((p.crop||"").toUpperCase())).length}{isMobile ? "" : " crops"}</span>}
          {(() => { const y = ppRowsYear(dedupPPRows(farmer.ppEligibility||[]).filter(p => !FALL_CROPS.includes((p.crop||"").toUpperCase())));
            return y && !isMobile ? <span style={{ marginLeft:5, fontSize:10, color:"#5a7a5a", fontWeight:700 }}>CY{y}</span> : null; })()}
        </button>
        {actions}
        <button style={T(tab==="docs")} onClick={()=>setTab("docs")}>
          📄 {isMobile ? "578" : "578 Documents"}
        </button>
        <button style={T(tab==="aph")} onClick={()=>setTab("aph")}>
          📊 APH{isMobile ? "" : " History"}
        </button>
        <button style={T(tab==="claims")} onClick={()=>setTab("claims")}>
          📋 Claims{isMobile ? "" : " History"}
        </button>
      </div>

      {/* ── APH HISTORY TAB ── */}
      {tab === "aph" && (
        <FarmerAPHHistory farmer={farmer} />
      )}

      {/* ── CLAIMS HISTORY TAB ── (spring crops only — wheat losses go on Fall tab) */}
      {tab === "claims" && (
        <FarmerClaimsHistory farmer={farmer} productFilter="crop" />
      )}

      {/* ── 578 DOCUMENTS TAB ── */}
      {/* A farmer sees their own stored 578s here (Kevin, 2026-11-02); only the
          File Management page is staff-only. customerView drops agency workflow. */}
      {tab === "docs" && (
        <Farmer578Documents farmer={farmer} season="spring" isMobile={isMobile} customerView={customerView} />
      )}

      {/* ── REVENUE GUARANTEE TAB ── */}
      {tab === "revenue" && (() => {
        // Spring tab shows ONLY crop APH revenue guarantee.
        // DRP, LRP, PRF each have their own dedicated pages.
        const hasAny = hasAPH;

        // Inject contract price for Processing Beans from metadata
        const pricesWithContract = procBeansPriceNum > 0
          ? repriceMap(prices, { "PROCESSING BEANS": procBeansPriceNum })
          : prices;

        // Crop total — revenue guarantee = sum of RMA base-plan liability (authoritative,
        // acre-weighted). Fall back to the APH calc only for rows with no liability yet.
        const cropTotal = springYields.reduce((s,r) => {
          if (r.liability != null) return s + Number(r.liability);
          const ac = r.acres != null ? Number(r.acres) : null;
          if (ac == null) return s;
          const { price, unit } = getCropPriceAndUnit(r, pricesWithContract, farmer.cropYear);
          const effPrice = unit === "lbs" ? price * 0.01 : price;
          return s + (r.approvedYield * effPrice * r.coverageLevel * ac);
        }, 0);
        const grandTotal = cropTotal;

        // Per-crop planted acres + revenue ($) + bushel guarantee, summed across
        // counties/units WITHIN a crop. None is summable ACROSS crops (corn bu ≠
        // soybean bu, and corn/soy are separate coverages), so the banner keeps
        // them per-crop. Revenue and bushels derive from RMA liability
        // (acre-weighted) with an APH-calc fallback.
        const guarByCrop = (() => {
          const m = {};
          springYields.forEach(r => {
            const { price, unit } = getCropPriceAndUnit(r, pricesWithContract, farmer.cropYear);
            // Elected price = projected × price-election fraction (1.0 normally, 0.55
            // for CAT). Without pePct, CAT production guarantees read ~45% low and CAT
            // revenue reads ~1.82× high. Matches the SOI-verified Dashboard method.
            const pePct = Number(r.priceElectionPct ?? r.price_election_pct) > 0 ? Number(r.priceElectionPct ?? r.price_election_pct) : 1;
            const effPrice = (unit === "lbs" ? price * 0.01 : price) * pePct;
            // Silage corn (unit tons) is a separate product from grain corn (bushels) —
            // key it onto its own line so tons never merge with the grain bushel total.
            const cropKey = (unit === "ton" && (r.crop || "").toUpperCase() === "CORN") ? "CORN SILAGE" : r.crop;
            const ppAc = ppFor(r);
            // Planted acres, on the same PP split the rest of the card uses.
            // A row with no acres on file contributes nothing and is counted as
            // missing so the banner can say so rather than under-report.
            const _split = plantedSplit(r, ppAc, unitsByKey);
            const plantedAc = r.acres != null ? _split.acres : null;
            let rev = null, bu = null;
            if (r.liability != null) {
              // Planted-only revenue guarantee (PP excluded — it's on the PP tab).
              rev = _split.liability;
              if (effPrice > 0 && rev != null) bu = rev / effPrice;
              else if (r.approvedYield != null && r.coverageLevel != null && r.acres != null) {
                // No price on file (e.g. grapes — no RMA table entry, M13 leaves
                // price_election null). Liability ÷ price is unavailable, so fall
                // back to the APH calc rather than reporting a 0 production guarantee.
                bu = r.approvedYield * r.coverageLevel * Math.max(0, Number(r.acres) - ppAc);
              }
            } else if (r.approvedYield != null && r.coverageLevel != null && r.acres != null) {
              const plantedAc = Math.max(0, Number(r.acres) - ppAc);
              rev = r.approvedYield * effPrice * r.coverageLevel * plantedAc;
              bu  = r.approvedYield * r.coverageLevel * plantedAc;
            }
            if (rev == null && bu == null) return;
            if (!m[cropKey]) m[cropKey] = { bu: 0, rev: 0, acres: 0, acresMissing: 0, ppAcres: 0, unit };
            // Prevent-plant acres ride along so a crop that was ALL prevent plant can
            // say so, rather than show a bare $0 and a dash that read like an error.
            m[cropKey].ppAcres += Number(ppAc) || 0;
            if (bu != null)  m[cropKey].bu  += bu;
            if (rev != null) m[cropKey].rev += rev;
            if (plantedAc != null) m[cropKey].acres += plantedAc;
            else m[cropKey].acresMissing += 1;
          });
          return Object.entries(m).map(([crop, v]) => ({ crop, ...v }))
            .sort((a, b) => b.rev - a.rev);
        })();

        // ⚠ A COVERAGE THE CARRIER HAS REPORTED ACRES FOR AND NO DOLLARS ON reads
        // exactly like a fully-priced one, because the guarantee above falls back to
        // APH × coverage × acres × price. Berwanger's CY2026 corn is that card: the
        // R&H file states 46 non-premium acres with no liability, no premium and no
        // subsidy, and the $23,749.11 on screen is this app's own model. See
        // src/coverageDollars.js — the dollars are NOT recoverable and must not be
        // invented; this only makes the silence askable.
        const silentNote = silentCoverageNote(springYields);

        return (
          <div>
            {!hasAny ? (
              <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #c8d8c8" }}>
                <div style={{ fontSize:32, marginBottom:10 }}>📄</div>
                <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:6 }}>No APH on file yet</div>
                <div style={{ color:"#60735f", fontSize:13 }}>Crop revenue guarantee data is populated automatically from the M13 feed. DRP, LRP, and PRF are shown on their own pages.</div>
              </Card>
            ) : (
              <>
                {/* ── SUMMARY BAR — per-crop revenue + bushel guarantee. Corn and soybeans
                       are separate coverages, so we never sum revenue or bushels across them. ── */}
                {guarByCrop.length > 0 && (
                  <Card style={{ marginBottom:20, background:"#1a2e1a", border:"none" }}>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:36, alignItems:"flex-start" }}>
                      {guarByCrop.map(c => (
                        <div key={c.crop}>
                          <div style={{ display:"flex", alignItems:"center", gap:7, fontSize:11, fontWeight:800, letterSpacing:"0.1em", color:"#8ab87a", textTransform:"uppercase", marginBottom:8 }}>
                            <span style={{ width:9, height:9, borderRadius:"50%", background:CROP_COLOR[c.crop]||"#8ab87a", display:"inline-block" }} />
                            {c.crop}
                          </div>
                          <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.1em", color:"#7aaa78", textTransform:"uppercase", marginBottom:2 }}>Revenue Guarantee</div>
                          {/* Whole dollars, like every other dollar figure on the card —
                              "$119,159.00" claimed a precision the cents do not have. */}
                          <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:24, color:"#fff", marginBottom:8 }}>{fmtD0(c.rev)}</div>
                          <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.1em", color:"#7aaa78", textTransform:"uppercase", marginBottom:2 }}>{c.unit === "bu" ? "Bushel Guarantee" : "Production Guarantee"}</div>
                          <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:18, color:"#fff", marginBottom:8 }}>{fmt(c.bu,0)}<span style={{ fontSize:11, fontWeight:600, color:"#a0c8a0", marginLeft:3 }}>{c.unit}</span></div>
                          {/* Planted acres — prevent-plant excluded, same split as the
                              revenue guarantee above and the county tables below. */}
                          <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.1em", color:"#7aaa78", textTransform:"uppercase", marginBottom:2 }}>Planted Acres</div>
                          <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:18, color:"#fff" }}>
                            {c.acres > 0 ? <>{fmt(c.acres,1)}<span style={{ fontSize:11, fontWeight:600, color:"#a0c8a0", marginLeft:3 }}>ac</span></> : "—"}
                            {!(c.acres > 0) && c.ppAcres > 0 && (
                              <div style={{ fontSize:12, fontWeight:600, color:"#e8c860", marginTop:3 }}>
                                all {fmt(c.ppAcres,1)} ac prevent plant — see the Prevent Plant tab
                              </div>
                            )}
                            {c.acresMissing > 0 && (
                              <span title={`${c.acresMissing} coverage line${c.acresMissing>1?"s":""} for ${c.crop} ha${c.acresMissing>1?"ve":"s"} no acres on file — acreage not reported yet. The acres shown exclude ${c.acresMissing>1?"them":"it"}.`}
                                    style={{ fontSize:12, color:"#e8c860", marginLeft:6, cursor:"help" }}>⚠</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* ── THE CARRIER HAS SAID NOTHING ABOUT THE MONEY ──
                       Named, never silently modelled. It states both readings —
                       not yet keyed, or withdrawn — because the file cannot tell
                       them apart and neither can we. */}
                {silentNote && (
                  <Card style={{ marginBottom:16, background:"#fdf8ec", border:"1px solid #e0cf9a" }}>
                    <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                      <span style={{ fontSize:15, lineHeight:"18px" }}>⚠</span>
                      <div>
                        <div style={{ fontWeight:800, fontSize:13, color:"#7a5c10", marginBottom:4 }}>
                          {silentNote.headline}
                        </div>
                        <div style={{ fontSize:12, color:"#6b5a2a", lineHeight:1.5 }}>{silentNote.detail}</div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* ── CROP REVENUE GUARANTEE ── */}
                {hasAPH && [...new Set([...springYields, ...scoEcoSpring].map(a => a.county))].map(county => {
                  const rpRows  = springYields.filter(a => a.county === county);
                  // Parenthesise the plan test. Without the inner brackets && binds
                  // tighter than ||, so this read as "(right county AND says SCO) OR
                  // plan is ARPI" — and EVERY ARPI row in the book passed, from any
                  // county. SCO is stored as plan 'ARPI', so a grower with soybean SCO
                  // in two counties saw both endorsements under both counties
                  // (Danielewicz: Niagara's $28,457 row showing again under Genesee,
                  // beside Genesee's own empty one). The ECO line below was always
                  // bracketed correctly, which is why only SCO duplicated.
                  const scoRows = scoEcoSpring.filter(s => s.county === county && ((s.plan||'').toUpperCase().includes('SCO') || (s.plan||'').toUpperCase() === 'ARPI'));
                  const ecoRows = scoEcoSpring.filter(s => s.county === county && (s.plan||'').toUpperCase().includes('ECO'));
                  const rows = rpRows; // alias for tfoot/countyTotal compat
                  const countyTotal = rpRows.reduce((s,r) => {
                    const ppAc = ppFor(r);
                    if (r.liability != null) return s + (plantedSplit(r, ppAc, unitsByKey).liability || 0);
                    const ac = r.acres != null ? Math.max(0, Number(r.acres) - ppAc) : null;
                    const { price, unit } = getCropPriceAndUnit(r, pricesWithContract, farmer.cropYear);
                    const effPrice = unit === "lbs" ? price * 0.01 : price;
                    return s + (ac ? r.approvedYield * effPrice * r.coverageLevel * ac : 0);
                  }, 0);
                  return (
                    <Card key={county} style={{ marginBottom:16 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                        <SectionTitle style={{ margin:0, border:"none", padding:0 }}>🌽 {county} County — Crop</SectionTitle>
                        {countyTotal > 0 && <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#2d4a2d", fontSize:15 }}>{fmtD0(countyTotal)}</span>}
                      </div>
                      <div style={{ overflowX:"auto" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                          <thead>
                            <tr style={{ background:"#f4f8f4" }}>
                              {/* Price/guarantee headers follow the crop's own yield unit — a
                                  county of grapes is priced $/ton and guaranteed in tons, not bu. */}
                              {(() => {
                                const units = [...new Set(rpRows.map(r => yieldUnit(r)))];
                                const u = units.length === 1 ? units[0] : null;
                                return ["Crop","Plan","Cov %","Appr. Yield", u ? `Price/${u}` : "Price",
                                  "Guar./Acre","Acres", u ? (u === "bu" ? "Bu Guar" : `${u} Guar`) : "Prod. Guar",
                                  "Liability","Gross Premium","Farmer Premium"];
                              })().map(h => (
                                <th key={h} style={{ padding:"8px 12px", textAlign:h==="Crop"?"left":"right", fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#60735f", textTransform:"uppercase", borderBottom:"1px solid #e4ece4", whiteSpace:"nowrap" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r,i) => {
                              const isProcessingBeans = (r.crop||"").toUpperCase() === "PROCESSING BEANS";
                              const { price, unit } = getCropPriceAndUnit(r, pricesWithContract, farmer.cropYear);
                              const isSilage   = unit === "ton" && !isProcessingBeans;
                              // Revenue guarantee is on PLANTED acres only. When this crop/county has
                              // prevent-plant acres, split the blended RMA liability and show the planted
                              // portion (PP guarantee lives on the Prevent Plant tab).
                              const ppAcres    = ppFor(r);
                              const uiAcres    = uiFor(r);
                              const effPrice   = unit === "lbs" ? price * 0.01 : price;   // $ per yield unit
                              // Units passed in so the planted liability is SUMMED from the planted
                              // units when they're complete, not modelled at the PP factor.
                              // ONE lookup, shared with statedGuaranteeBu below, so the planted split and
                              // the stated guarantee can never disagree about which units belong here.
                              const _uRows        = unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)];
                              const _pv        = plantedView(r, ppAcres, r.approvedYield * effPrice * r.coverageLevel, _uRows);
                              const acres      = _pv.acres;
                              const liability  = _pv.liability;
                              // Guarantee figures derive from RMA's authoritative liability when present —
                              // it's acre-weighted across ALL the policy's units, so on multi-unit policies
                              // it sits above the simple-average APH × price × cov calc. Fall back to that
                              // base-APH calc only for rows with no liability yet (e.g. acreage not reported).
                              const gpa        = _pv.gpa;
                              // Bushel (production) guarantee via the shared guaranteeBushels() — liability
                              // ÷ price (effective acre-weighted guaranteed bushels); fallback = APH ×
                              // coverage% × planted acres. Planted liability/acres (PP removed) passed in.
                              const buGuar     = guaranteeBushels(liability, effPrice, r.approvedYield, r.coverageLevel, acres, (r.priceElectionPct ?? r.price_election_pct), statedGuaranteeBu(_uRows));
                              const grossPrem  = r.totalPremium != null ? Number(r.totalPremium) : null;
                              const farmerPrem = r.netPremium != null ? Number(r.netPremium)
                                : (grossPrem != null && r.subsidy != null) ? grossPrem - Number(r.subsidy)
                                : grossPrem;
                              const isLowYield = isSilage
                                ? (r.approvedYield != null && r.approvedYield < 8)
                                : ((r.crop==="CORN"&&r.approvedYield<50)||(r.crop==="SOYBEANS"&&r.approvedYield<20)||(r.crop==="WHEAT"&&r.approvedYield<20)||(r.crop==="DRY BEANS"&&r.approvedYield<500));
                              // Same crop+practice+type rule as the rows it gates — otherwise the
                              // "has supplemental" flag disagrees with what actually renders.
                              const _supp      = matchSupplementals(scoRows, ecoRows, r, rows);
                              const hasSuppCov = _supp.any();
                              return (
                                <React.Fragment key={i}>
                                <tr style={{ borderBottom: hasSuppCov ? "none" : "1px solid #f0f4f0" }}>
                                  <td style={{ padding:"10px 12px" }}>
                                    <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                                      <div style={{ width:9, height:9, borderRadius:"50%", background:CROP_COLOR[r.crop]||"#888", flexShrink:0 }} />
                                      <span style={{ fontWeight:700 }}>{r.crop}{r.type ? ` · ${isCurrentUserAdmin() ? r.type : customerTypeLabel(r.type)}` : ""}{normalizePractice(r.practice) === "Organic" ? <span style={{ fontWeight:600, color:"#5a7a5a", marginLeft:3, fontSize:11 }}> · Organic</span> : ""}</span>
                                    </div>
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right" }}><Pill color={CROP_COLOR[r.crop]}>{r.plan}</Pill></td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#2d4a2d" }}>{covLevelText(r.coverageLevel, r.coverageType)}</td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                                    <div>
                                      {/* Low APHs (tons/acre on grapes, silage) need a decimal —
                                          at 8.7 a whole number is a 3% swing in the guarantee. */}
                                      <span>{fmt(r.approvedYield, r.approvedYield != null && Math.abs(r.approvedYield) < 20 ? 1 : 0)} {unit}</span>
                                      {isLowYield && (
                                        <span title="Unusually low yield — verify APH. Units may have been averaged incorrectly." style={{marginLeft:5,fontSize:10,color:"#c0392b",fontWeight:800,cursor:"help"}}>⚠️ low</span>
                                      )}
                                      {/* The stored APH is an UNWEIGHTED mean of the unit yields; RMA
                                          prices off the acre-weighted one. Show the weighted figure
                                          when we have per-unit acres, and let the count open the
                                          units exactly as insured. */}
                                      {(() => {
                                        const _uk = cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode);
                                        // PLANTED units only. This row shows the planted guarantee, so
                                        // its acre-weighted APH must be weighted over planted acres —
                                        // including a PP unit weighted 203.3 over 577.7 insured acres
                                        // on a line reading 533.4 planted.
                                        const _us = plantedUnits(unitsByKey[_uk]);
                                        const _w  = weightedAph(_us);
                                        if (!(r.unitCount > 1) && _us.length < 2) return null;
                                        return (
                                          <div style={{ marginTop: 2 }}>
                                            <button onClick={() => toggleUnits(_uk)} disabled={!_us.length}
                                              title={_us.length ? "Show each unit's acres, APH and liability as insured"
                                                                : "Unit detail appears after the next sync"}
                                              style={{ fontSize: 10, color: "#9a7a3a", fontWeight: 600, fontFamily: "sans-serif",
                                                       background: "none", border: "none", padding: 0,
                                                       cursor: _us.length ? "pointer" : "default",
                                                       textDecoration: _us.length ? "underline dotted" : "none" }}>
                                              {_us.length || r.unitCount} unit{(_us.length || r.unitCount) === 1 ? "" : "s"}
                                              {_us.length ? (openUnits.has(_uk) ? " ▲" : " ▼") : ""}
                                            </button>
                                            {_w != null && Math.abs(_w - (r.approvedYield || 0)) >= 0.5 && (
                                              <div style={{ fontSize: 10, color: T.faint, fontWeight: 600 }}
                                                   title="Acre-weighted across the units — what RMA prices off. The figure above is the unweighted mean.">
                                                {_w} wtd
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                                    {isProcessingBeans && !isCurrentUserAdmin() ? (
                                      Number(procBeansPrice) > 0 ? `${fmtD(Number(procBeansPrice))}/ton` : <span style={{ color:"#ccc" }}>—</span>
                                    ) : isProcessingBeans ? (
                                      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:4 }}>
                                        <span style={{ fontSize:11, color:"#7a5a30" }}>$</span>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          placeholder="0.00"
                                          value={procBeansPrice}
                                          readOnly={!isCurrentUserAdmin()}
                                          onChange={e => setProcBeansPrice(e.target.value)}
                                          onBlur={e => { if (isCurrentUserAdmin()) saveProcBeansPrice(e.target.value); }}
                                          style={{ width:64, padding:"3px 5px", borderRadius:5, border:"1px solid #c0a080", fontSize:12, fontVariantNumeric: "tabular-nums", textAlign:"right", color:"#7a3b10" }}
                                        />
                                        <span style={{ fontSize:11, color:"#7a5a30" }}>/ton</span>
                                      </div>
                                    ) : (
                                      !(price > 0)
                                        // No price election on file and no RMA table entry for this
                                        // crop (grapes, apples, …). Showing $0.00 reads as a real
                                        // zero price; "—" says "unknown" — add one on the Prices screen.
                                        ? <span style={{ color:"#ccc" }} title={`No price on file for ${r.crop}. Add one on the Prices screen to compute the per-${unit} guarantee.`}>—</span>
                                      : unit === "lbs"
                                        ? `${fmtD(price / 100)}/lbs`
                                        : `${fmtD(price)}/${unit}`
                                    )}
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right" }}
                                    title={liability != null && acres
                                      ? `RMA revenue guarantee per acre = liability ÷ acres ($${fmt(liability,0)} ÷ ${fmt(acres,1)} ac). On multi-unit policies this is acre-weighted across all ${r.unitCount||''} units, so it can differ from the simple APH × price × coverage (${fmtD(r.approvedYield * effPrice * r.coverageLevel)}).`
                                      : `APH × price × coverage = ${fmt(r.approvedYield)} × ${fmtD(price)} × ${(r.coverageLevel*100).toFixed(0)}%`}>
                                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:CROP_COLOR[r.crop]||"#555" }}>{fmtD(gpa)}<span style={{ fontSize:10, color:"#aaa", fontWeight:400, marginLeft:2 }}>/ac</span></span>
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}
                                    title={ppAcres > 0 ? `Planted acres — ${fmt(ppAcres,1)} prevent-plant ac excluded (see Prevent Plant tab). Revenue guarantee is on planted acres only.` : undefined}>
                                    {acres != null
                                      ? <span style={{ fontWeight:700 }}>{fmt(acres,1)}{ppAcres > 0 && <span style={{ fontSize:10, color:"#9a5a1a", fontWeight:700, marginLeft:3 }}>planted</span>}</span>
                                      : <span style={{ color:"#ccc" }}>—</span>}
                                    {/* Acres carrying no guarantee ON THIS CROP LINE — $0 liability
                                        here — which the SOI reports under UNINSURABLE, outside its
                                        INSURED total. Do NOT tell the grower RMA insured nothing on
                                        the ACRES: when the cause is prevent plant beyond this crop's
                                        eligibility, the AIP ROLLS them onto another crop and pays
                                        them there. Fire Creek 2026 (MP-0935914) is the worked case —
                                        56.7 Ontario corn ac exceeded corn's 23.7 max eligible PP and
                                        were rolled to 56.3 soybeans + 0.4 dry beans, carrying
                                        $12,612 + $73 of PP liability. The M13 file does not link the
                                        two sides, so the roll is NOT inferred here; the label says
                                        only what is verifiable and points at the document that
                                        settles it. */}
                                    {uiAcres > 0 && (
                                      <div style={{ fontSize: 10.5, color: T.faint, fontWeight: 600 }}
                                           title={`${fmt(uiAcres,1)} ac carry no guarantee on THIS crop line ($0 liability) and sit outside its insured acres — the SOI reports them under UNINSURABLE. This does NOT mean the acres are uncovered: if they were prevent plant beyond this crop's eligibility, the AIP may have ROLLED them onto another crop and paid them there. Check the AIP's Prevented Planting Worksheet before telling a grower these acres carry nothing.`}>
                                        +{fmt(uiAcres,1)} {isCurrentUserAdmin() ? "uninsurable here" : "ac no guarantee on this line"}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                                    <BuGuarCell derived={buGuar} unit={unit} color="#2d4a2d"
                                      units={unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)]} />
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                                    {liability != null ? <span style={{ fontWeight:700, color:"#1a2e1a" }}>{fmtD0(liability)}</span> : <span style={{ color:"#bbb" }}>—</span>}
                                  </td>
                                  {/* Premium is on INSURED acres — the acres column beside it is
                                      planted. premiumBasisNote() names both, and only when a row
                                      actually carries PP. */}
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}
                                      title={premiumBasisNote(r, ppAcres)}>
                                    {grossPrem != null ? <span style={{ fontWeight:700, color:"#1a2e1a" }}>{fmtD0(grossPrem)}</span> : <span style={{ color:"#bbb" }}>—</span>}
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}
                                      title={premiumBasisNote(r, ppAcres)}>
                                    {farmerPrem != null
                                      ? <div style={{ display:"inline-block", background:"#eef6ec", border:"1px solid #4a7a2a33", borderRadius:6, padding:"3px 9px" }}>
                                          <span style={{ fontWeight:800, color:"#2d5a10" }}>{fmtD0(farmerPrem)}</span>
                                          {ppAcres > 0 && <span style={{ fontSize:10, color:"#9a5a1a", fontWeight:700, marginLeft:4 }}>incl. PP</span>}
                                        </div>
                                      : <span style={{ color:"#bbb" }}>—</span>}
                                  </td>
                                </tr>
                                {/* SCO/ECO sub-rows — same pattern as FallView */}
                                {/* Match on CROP as well as practice/type — the other two
                                    renderers already do. Without it a supplemental nests under
                                    the wrong crop whenever type is blank on both (Danielewicz's
                                    soybean SCO matching a corn line), and fall crops routinely
                                    share type 'W' so wheat's SCO lands under triticale. All
                                    three paths now key on county + crop + practice + type. */}
                                {[..._supp.sco.map(s=>({...s,_tag:'SCO'})), ..._supp.eco.map(s=>({...s,_tag:'ECO'}))
                                ].map((s, si, arr) => {
                                  const isLast = si === arr.length - 1;
                                  const isSCO = s._tag === 'SCO';
                                  const baseCov  = r.coverageLevel ? (r.coverageLevel*100).toFixed(0) : '75';
                                  const ecoCov   = s.coverageLevel ? (s.coverageLevel*100).toFixed(0) : '95';
                                  const bandLow  = isSCO ? baseCov : '86';
                                  const bandHigh = isSCO ? '86' : ecoCov;
                                  const color    = isSCO ? '#7a5a1a' : '#4a6a8a';
                                  const bg       = isSCO ? '#fdf8ee' : '#f0f5fa';
                                  const sAcres   = s.acres != null && Number(s.acres) > 0 ? Number(s.acres) : null;
                                  const sLiab    = s.liability != null ? Number(s.liability) : null;
                                  const sGross   = s.totalPremium != null ? Number(s.totalPremium) : null;
                                  const sFarmer  = s.netPremium != null ? Number(s.netPremium)
                                    : (sGross != null && s.subsidy != null) ? sGross - Number(s.subsidy)
                                    : sGross;
                                  const guarPerAcre = (sLiab != null && sAcres != null) ? sLiab / sAcres : null;
                                  return (
                                    <tr key={`sco-eco-${si}`} style={{ borderBottom: isLast ? "1px solid #f0f4f0" : "none", background: bg }}>
                                      <td style={{ padding:"6px 12px 6px 24px", color:"#888", fontSize:11 }}>
                                        <span style={{ marginRight:5, color:"#ccc" }}>└</span>
                                        <span style={{ color, fontStyle:"italic", fontWeight:600 }}>supplemental</span>
                                      </td>
                                      <td style={{ padding:"6px 12px", textAlign:"right" }}><Pill color={color}>{s.plan === 'ARPI' ? 'SCO-RP' : s.plan}</Pill></td>
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:11, color, fontWeight:700 }}>{bandLow}–{bandHigh}%</td>
                                      <td style={{ padding:"6px 12px", textAlign:"right", color:"#ccc", fontSize:11 }}>—</td>
                                      <td style={{ padding:"6px 12px", textAlign:"right", color:"#ccc", fontSize:11 }}>—</td>
                                      <td style={{ padding:"6px 12px", textAlign:"right" }}>
                                        {guarPerAcre != null
                                          ? <span style={{ fontVariantNumeric: "tabular-nums", fontSize:12, fontWeight:700, color }}>{fmtD(guarPerAcre)}<span style={{fontWeight:400,color:"#aaa",fontSize:10,marginLeft:2}}>/ac</span></span>
                                          : <span style={{ color:"#ccc", fontSize:11 }}>—</span>}
                                      </td>
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12, color:"#666" }}>
                                        {sAcres != null ? fmt(sAcres,2) : <span style={{color:"#ccc"}}>—</span>}
                                      </td>
                                      <td style={{ padding:"6px 12px", textAlign:"right", color:"#ccc", fontSize:11 }}>—</td>
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12 }}>
                                        {sLiab != null ? <span style={{ fontWeight:700, color }}>{fmtD0(sLiab)}</span> : <span style={{color:"#ccc"}}>—</span>}
                                      </td>
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12 }}>
                                        {sGross != null ? <span style={{ fontWeight:700, color }}>{fmtD0(sGross)}</span> : <span style={{color:"#ccc"}}>—</span>}
                                      </td>
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12 }}>
                                        {sFarmer != null ? <span style={{ fontWeight:700, color }}>{fmtD0(sFarmer)}</span> : <span style={{color:"#ccc"}}>—</span>}
                                      </td>
                                    </tr>
                                  );
                                })}
                                {/* Units exactly as insured — opened from the unit count above. */}
                                {openUnits.has(cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)) && (
                                  <CoverageUnitRows
                                    units={unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)]}
                                    colSpan={11} yieldUnit={unit} />
                                )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                          {/* County total row — sums base + SCO + ECO */}
                          {(() => {
                            const allRows = [...rpRows, ...scoRows, ...ecoRows];
                            // Base RP totals are PLANTED-only (prevent-plant split out, matching the
                            // rows above). SCO/ECO supplementals keep their stored area-plan liability.
                            const totAcres  = rpRows.reduce((s,r) =>
                              s + (r.acres != null ? (plantedSplit(r, ppFor(r), unitsByKey).acres || 0) : 0), 0);
                            // No bushel total in the footer — corn bu and soybean bu can't be
                            // summed, so a per-crop footer just restates the rows above it.
                            const totLiab = rpRows.reduce((s,r) => s + (plantedSplit(r, ppFor(r), unitsByKey).liability || 0), 0)
                              + [...scoRows, ...ecoRows].reduce((s,r) => s + (r.liability ? Number(r.liability) : 0), 0);
                            const totGross  = allRows.reduce((s,r) => s + (r.totalPremium ? Number(r.totalPremium) : 0), 0);
                            const totFarmer = allRows.reduce((s,r) => {
                              if (r.netPremium != null) return s + Number(r.netPremium);
                              if (r.totalPremium != null && r.subsidy != null) return s + Number(r.totalPremium) - Number(r.subsidy);
                              return s + (r.totalPremium ? Number(r.totalPremium) : 0);
                            }, 0);
                            if (totLiab === 0 && totGross === 0) return null;
                            return (
                              <tfoot>
                                <tr style={{ background:"#eef4ee", borderTop:"2px solid #d0e4d0" }}>
                                  <td colSpan={6} style={{ padding:"8px 12px", fontSize:11, fontWeight:800, color:"#2d5a10", textTransform:"uppercase", letterSpacing:"0.06em" }}>County Total</td>
                                  <td style={{ padding:"8px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#1a2e1a" }}>{totAcres > 0 ? fmt(totAcres,2) : "—"}</td>
                                  <td style={{ padding:"8px 12px" }} />
                                  <td style={{ padding:"8px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#1a2e1a" }}>{totLiab > 0 ? fmtD0(totLiab) : "—"}</td>
                                  <td style={{ padding:"8px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#1a2e1a" }}>{totGross > 0 ? fmtD0(totGross) : "—"}</td>
                                  <td style={{ padding:"8px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#2d5a10" }}>{totFarmer > 0 ? fmtD0(totFarmer) : "—"}</td>
                                </tr>
                              </tfoot>
                            );
                          })()}
                        </table>
                      </div>
                    </Card>
                  );
                })}

                {/* What the AIP will actually invoice. Sits below the guarantee
                    tables because the guarantee is the headline — but a farm that
                    took a claim reads HIGH against its own bill by exactly the
                    credit, and nothing on this card used to say so. */}
                <PolicyBillingPanel
                  farmer={farmer}
                  crops={[...new Set(springYields.map(a => a.crop))]}
                  tone={{ accent:"#2d5a10", bg:"#f4f8f4", rule:"#d0e4d0" }} />

              </>
            )}
          </div>
        );
      })()}

      {/* ── PREVENT PLANT TAB ── */}
      {tab === "pp" && (() => {
        const springPPRows = dedupPPRows(farmer.ppEligibility||[]).filter(p => !FALL_CROPS.includes((p.crop||"").toUpperCase()));
        return (
        <div>
          {hasPP && (
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
              <button
                onClick={() => generatePolicyPDF(farmer, "pp", prices)}
                style={{ background:"#1a2e1a", color:"#fff", border:"none", borderRadius:7, padding:"8px 16px", cursor:"pointer", fontSize:12, fontWeight:700, letterSpacing:"0.06em", display:"flex", alignItems:"center", gap:6 }}
                title="Generate a one-page PP summary with educational refresher + this farmer's eligibility numbers"
              >
                🖨 Print PP Summary
              </button>
            </div>
          )}
          {!hasPP ? (
            <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #c8d8c8" }}>
              <div style={{ fontSize:32, marginBottom:10 }}>📋</div>
              <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:6 }}>No PP Eligibility Report on file</div>
              <div style={{ color:"#60735f", fontSize:13 }}>Upload a PP Eligibility Report PDF to see prevent plant eligibility and payment per acre.</div>
            </Card>
          ) : (
            // ⚠ THE SPRING TABLE MUST EXCLUDE FALL CROPS, AND IT DID NOT. Its own tab
            // COUNT already filtered them ("9 crops"), so the count and the table
            // disagreed — and after the crop years split, the CY2026 spring tab was
            // rendering CY2027 WHEAT eligibility with a CY2026 price beside it.
            // FallView has always filtered; this is Spring catching up.
            [...new Set(springPPRows.map(p => p.county))].map(county => {
              const ppRows = springPPRows.filter(p => p.county === county);
              const tableRows = ppRows.map(pp => {
                const ppCountyUp   = (pp.county||"").toUpperCase().trim();
                const ppCropUp     = (pp.crop||"").toUpperCase();
                const ppTypeUp     = (pp.type||"").toUpperCase();
                // A generic PP row (type='ALL' or 'No Practice') should match
                // any approvedYield on the same county+crop, regardless of the
                // specific TBL/Non-Irrigated combo crop_coverage stores. Without
                // this, potatoes (and any crop with PP-as-ALL semantics) show
                // empty APH yield and an uncalculable PP Guarantee.
                const ppGeneric = isGenericPPRow(pp.type, pp.practice);
                const ppTypeCode = normalizeCropType(pp.type);
                const ayStrict = (farmer.approvedYields||[]).filter(a =>
                  (a.county||"").toUpperCase().trim() === ppCountyUp &&
                  (a.crop||"").toUpperCase() === ppCropUp &&
                  (ppGeneric || !ppTypeUp || normalizeCropType(a.type) === ppTypeCode)
                );
                // pp_eligibility uses parent commodity types (SOYBEANS COMM/SSFG, POTATOES ALL)
                // while crop_coverage uses MPCI sub-types (DC, NTS, TBL). When strict-type
                // match yields nothing, fall back to any approvedYield on the same county+crop
                // so the PP guarantee can still be computed — but NEVER cross the grain/silage
                // boundary (016 bu vs 026 tons): a silage PP row borrowing a grain APH yield
                // priced at $/ton inflates the guarantee ~5× (see ppAyUnitCompatible).
                const ayBase = ayStrict.length > 0 ? ayStrict : (farmer.approvedYields||[]).filter(a =>
                  (a.county||"").toUpperCase().trim() === ppCountyUp &&
                  (a.crop||"").toUpperCase() === ppCropUp &&
                  (normalizeCropType(a.type) === "026") === (ppTypeCode === "026")
                );
                // Exact practice match first, then any row with no practice on either side, then first match
                const ay = ayBase.find(a => normalizePractice(a.practice).toUpperCase() === normalizePractice(pp.practice).toUpperCase())
                  || ayBase.find(a => !(a.practice||"") && !(pp.practice||""))
                  || ayBase[0]
                  || null;
                const isSilage   = normalizeCropType(pp.type) === "026";
                const isPotatoes = (pp.crop||"").toUpperCase() === "POTATOES";
                const isDryBeans = (pp.crop||"").toUpperCase() === "DRY BEANS";
                const price = isSilage ? (RMA_SILAGE_PRICES[farmer.cropYear] || RMA_SILAGE_PRICES[2026])
                  : isPotatoes ? (ay?.price || prices["POTATOES"] || 17.85)
                  : isDryBeans ? (() => {
                      // pp.type often empty on SOI imports — fall back to linked ay.type, then BTS.
                      const beanType = ((pp.type || ay?.type) || "").toUpperCase();
                      const isOrg = normalizePractice(pp.practice) === "Organic";
                      const typeKey = isOrg && beanType ? `${beanType}_ORGANIC` : beanType;
                      return (typeKey && prices[typeKey]) || prices["BTS"] || 0;
                    })()
                  : (prices[pp.crop] || 0);
                // The prevent-plant unit RMA actually keyed, if there is one: its
                // stated Acre Guarantee Quantity is the elected factor as a READING
                // rather than a model, and outranks the options string below it.
                const _ppUnits = ay ? unitsByKey[cuKey(ay.county, ay.crop, ay.plan, ay.typeCode, ay.practiceCode)] : null;
                const maxAc = maxAcres(pp);
                const _ppb = ppPerAcreForRow(pp, ay, prices, farmer.cropYear, _ppUnits);
                const perAcre = _ppb.perAcre;
                const displayYield = pp.aphYield ?? ay?.approvedYield ?? null;
                const displayCov   = pp.coverageLevel ?? ay?.coverageLevel ?? null;
                const displayPlan  = ay?.plan ?? null;
                const unit = yieldUnit({type: pp.type, crop: pp.crop});
                const isCat = ay?.coverageType === "C";
                return { ...pp, ay, price, unit, maxAc, perAcre, perAcreHigh: _ppb.perAcreHigh, ppf: _ppb.ppf, displayYield, displayCov, displayPlan, isCat,
                         refused: _ppb.refused || null, refusedYears: _ppb.refusedYears || null };
              }).filter(r => silageHasApprovedAph(normalizeCropType(r.type) === "026", r.aphYield, r.ay));

              if (!tableRows.length) return null; // county left with nothing after silage filter

              return (
                <Card key={county} style={{ marginBottom:16 }}>
                  <SectionTitle>{county} County</SectionTitle>
                  {isMobile ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {tableRows.map((r,i) => (
                        <div key={i} style={{ background:CROP_BG[r.crop]||"#f8faf8", border:`1px solid ${CROP_COLOR[r.crop]||"#e4ece4"}44`, borderRadius:8, padding:"12px 14px" }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                              <div style={{ width:10, height:10, borderRadius:"50%", background:CROP_COLOR[r.crop]||"#888" }} />
                              <span style={{ fontWeight:800, fontSize:14, color:"#1a2e1a" }}>{r.crop}{r.type ? ` · ${r.type}` : ""}</span>
                              {r.displayPlan && <Pill color={CROP_COLOR[r.crop]}>{r.displayPlan}</Pill>}
                            </div>
                            {r.perAcre != null && (
                              <div style={{ background:"#fff", border:`1px solid ${CROP_COLOR[r.crop]}44`, borderRadius:6, padding:"5px 12px", textAlign:"center" }}>
                                <div style={{ fontSize:10, color:"#60735f", marginBottom:1 }}>PP Guarantee</div>
                                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:CROP_COLOR[r.crop], fontSize:16 }}>{r.ppf && !r.ppf.exact ? <span title={r.ppf.note} style={{ fontSize:10, color:"#9a7a40", marginRight:3 }}>at least</span> : null}{fmtD(r.perAcre)}</span>
                                <span style={{ fontSize:10, color:"#aaa", marginLeft:2 }}>/ac</span>
                              </div>
                            )}
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"6px 12px", fontSize:12 }}>
                            <div><span style={{ color:"#60735f", fontSize:10, display:"block" }}>Coverage</span><span style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#2d4a2d" }}>{covLevelText(r.displayCov, r.isCat ? "C" : null)}</span></div>
                            <div><span style={{ color:"#60735f", fontSize:10, display:"block" }}>APH Yield</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{r.displayYield!=null?fmt(r.displayYield)+(" "+yieldUnit(r)):"—"}</span></div>
                            <div><span style={{ color:"#60735f", fontSize:10, display:"block" }}>PP Factor</span><span title={r.ppf ? r.ppf.note : undefined} style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, color: r.ppf && !r.ppf.exact ? "#9a7a40" : "#6b4f00" }}>{r.ppf ? ppFactorLabel(r.ppf) : (getPPFactor(r.crop)*100).toFixed(0)+"%"}</span></div>
                            <div><span style={{ color:"#60735f", fontSize:10, display:"block" }}>Max Elig. Acres</span><span style={{ fontVariantNumeric: "tabular-nums", fontWeight:700 }}>{fmt(r.maxAc,2)}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                      <thead>
                        <tr style={{ background:"#f4f8f4" }}>
                          {["Crop / Type","Plan","Coverage","APH Yield","Price","PP Factor","Max Elig. Acres","PP Guarantee / Acre"].map(h => (
                            <th key={h} style={{ padding:"8px 14px", textAlign:h==="Crop / Type"?"left":"right", fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#60735f", textTransform:"uppercase", borderBottom:"1px solid #e4ece4", whiteSpace:"nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((r,i) => (
                          <tr key={i} style={{ borderBottom:"1px solid #f0f4f0" }}>
                            <td style={{ padding:"11px 14px" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                                <div style={{ width:10, height:10, borderRadius:"50%", background:CROP_COLOR[r.crop]||"#888", flexShrink:0 }} />
                                <span style={{ fontWeight:700, color:"#1a2e1a" }}>{r.crop}{r.type ? ` · ${r.type}` : ""}</span>
                              </div>
                            </td>
                            <td style={{ padding:"11px 14px", textAlign:"right" }}>
                              {r.displayPlan ? <Pill color={CROP_COLOR[r.crop]}>{r.displayPlan}</Pill> : "—"}
                            </td>
                            <td style={{ padding:"11px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", color:"#2d4a2d", fontWeight:700 }}>
                              {covLevelText(r.displayCov, r.isCat ? "C" : null)}
                            </td>
                            <td style={{ padding:"11px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                              {r.displayYield != null ? fmt(r.displayYield) + " " + yieldUnit(r) : "—"}
                            </td>
                            <td style={{ padding:"11px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                              {r.unit === "lbs" ? `${fmtD(r.price/100)}/lbs` : fmtD(r.price)}
                            </td>
                            <td style={{ padding:"11px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#6b4f00" }}>
                              <span title={r.ppf ? r.ppf.note : undefined}>{r.ppf ? ppFactorLabel(r.ppf) : (getPPFactor(r.crop)*100).toFixed(0)+"%"}</span>
                            </td>
                            <td style={{ padding:"11px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#1a2e1a" }}>
                              {fmt(r.maxAc,2)}
                            </td>
                            <td style={{ padding:"11px 14px", textAlign:"right" }}>
                              {r.perAcre != null
                                ? <div style={{ display:"inline-block", background:CROP_BG[r.crop]||"#f4f8f4", border:`1px solid ${CROP_COLOR[r.crop]}44`, borderRadius:6, padding:"5px 12px" }}>
                                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:CROP_COLOR[r.crop], fontSize:15 }}>{r.ppf && !r.ppf.exact ? <span title={r.ppf.note} style={{ fontSize:10, color:"#9a7a40", marginRight:3 }}>at least</span> : null}{fmtD(r.perAcre)}</span>
                                    <span style={{ fontSize:10, color:"#aaa", marginLeft:3 }}>/ac</span>
                                  </div>
                                : <span style={{ color:"#ccc" }}>—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* 4-year acreage history */}
                  <details style={{ marginTop:14 }}>
                    <summary style={{ fontSize:11, color:"#60735f", cursor:"pointer", fontWeight:700, userSelect:"none" }}>▸ 4-YEAR ACREAGE HISTORY</summary>
                    <div style={{ marginTop:10, overflowX:"auto" }}>
                      <table style={{ fontSize:12, borderCollapse:"collapse", width:"100%" }}>
                        <thead><tr>
                          {["Crop / Type",
                            ppRows[0]?.yr1Year ? String(ppRows[0].yr1Year) : "Yr1",
                            ppRows[0]?.yr2Year ? String(ppRows[0].yr2Year) : "Yr2",
                            ppRows[0]?.yr3Year ? String(ppRows[0].yr3Year) : "Yr3",
                            ppRows[0]?.yr4Year ? String(ppRows[0].yr4Year) : "Yr4",
                            "Max Elig."
                          ].map(h => (
                            <th key={h} style={{ padding:"6px 10px", textAlign:h==="Crop / Type"?"left":"right", color:"#60735f", fontWeight:700, borderBottom:"1px solid #e4ece4", whiteSpace:"nowrap" }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {ppRows.map((pp,i) => (
                            <tr key={i} style={{ borderBottom:"1px solid #f5f7f5" }}>
                              <td style={{ padding:"6px 10px", fontWeight:600, color:"#1a2e1a" }}>{pp.crop}{pp.type?` · ${pp.type}`:""}</td>
                              {[pp.yr1,pp.yr2,pp.yr3,pp.yr4].map((ac,j) => (
                                <td key={j} style={{ padding:"6px 10px", textAlign:"right", fontVariantNumeric: "tabular-nums", color:ac>0?"#1a2e1a":"#ccc" }}>
                                  {ac > 0 ? fmt(ac,2) : "—"}
                                </td>
                              ))}
                              <td style={{ padding:"6px 10px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:CROP_COLOR[pp.crop]||"#1a2e1a" }}>
                                {fmt(maxAcres(pp),2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </Card>
              );
            })
          )}
        </div>
        );
      })()}

    </div>
  );
}

// FALL VIEW — wheat, barley, triticale, rye — Revenue Guarantee tab + Prevent Plant tab (mirrors SpringView)
// ── THE FALL SECTION SHOWS ONE CROP YEAR AT A TIME ─────────────────────────
// Kevin, 2026-09-15/16: wheat moves to CY2027 as soon as its price guarantee exists;
// CY2026 stays preserved and one click away; and "we can't cross contaminate two
// years data". So the section is scoped to a crop year, and every figure on it comes
// from rows of THAT year. A prior-year figure may be SHOWN beside it, labelled with
// its own year, and is never an input to anything.
//
// Which crop years this farm has fall paper in — coverage, SCO/ECO lines or prevent-
// plant eligibility. Newest first.
function fallYearsOnFile(farmer) {
  const isFall = (c) => FALL_CROPS.includes(String(c || "").toUpperCase());
  const ys = new Set();
  for (const [y, rows] of Object.entries((farmer && farmer.approvedYieldsByYear) || {}))
    if ((rows || []).some(r => isFall(r.crop))) ys.add(Number(y));
  for (const e of (farmer && farmer.scoEcoEndorsements) || [])
    if (e && e.cropYear != null && isFall(e.crop)) ys.add(Number(e.cropYear));
  for (const [y, rows] of Object.entries((farmer && farmer.ppEligibilityByYear) || {}))
    if ((rows || []).some(r => isFall(r.crop))) ys.add(Number(y));
  return [...ys].filter(y => Number.isFinite(y) && y > 0).sort((a, b) => b - a);
}
// ⚠ NOT A HIDDEN GLOBAL SWITCH. This picks only which year the Fall section OPENS on,
// and the reader can switch. The date comes from cropRolloverYear — the day wheat's
// projected-price window closes, derived per year, never typed — so it opens on CY2027
// from 2026-09-15 and on CY2028 from 2027-09-15, and before that on the year that has
// arrived. A farm with no paper in the rolled year opens on its newest year that is.
function defaultFallYear(years, todayISO) {
  if (!years || !years.length) return null;
  const rolled = cropRolloverYear("WHEAT", todayISO) ?? CURRENT_CROP_YEAR;
  const onOrBefore = years.filter(y => y <= rolled);
  return onOrBefore.length ? Math.max(...onOrBefore) : Math.min(...years);
}
// ⚠ A YEAR EVERY BASE LINE OF WHICH THE PRICE MAP CAN ACTUALLY PRICE. A map merely TAGGED
// with the year is not proof: _pricesFor(2027) returns a map holding only per-type grape and
// onion prices, so a CY2027 wheat row fell through getCropPriceAndUnit to the conventional
// ladder price — right for conventional wheat, wrong for ORGANIC, and $0.00 for barley. An
// organic line needs the organic price; nothing borrows the conventional one.
function fallYearPriceable(rows, prices) {
  const base = (rows || []).filter(r => {
    const p = String(r.plan || "").toUpperCase();
    return !(p.includes("SCO") || p.includes("ECO") || p === "ARPI");
  });
  if (!prices || !base.length) return false;
  return base.every(r => {
    const crop = String(r.crop || "").toUpperCase();
    return Number(prices[normalizePractice(r.practice) === "Organic" ? crop + "_ORGANIC" : crop]) > 0;
  });
}
// THE PRICE KEY A FALL LINE IS PRICED UNDER. Certified organic carries its own RMA election and
// is priced as "<CROP>_ORGANIC"; transitional organic (714 / 712) is priced conventional by RMA.
// A line whose organic practice code is not stated is treated as certified — every organic fall
// line in the book is 713 (measured 2026-09-17) — and a transitional code always wins.
function fallPriceKey(crop, practice, row) {
  const c = String(crop || "").toUpperCase();
  if (normalizePractice(practice) !== "Organic") return c;
  const code = String((row && (row.practiceCode ?? row.practice_code)) || "");
  return code === "714" || code === "712" ? c : c + "_ORGANIC";
}
// Every price a set of lines is priced under, conventional first then organic, once each.
function fallPriceKeys(lines) {
  const keys = [...new Set((lines || []).map(l => fallPriceKey(l.crop, l.practice, l.row)))];
  return [...keys.filter(k => !k.endsWith("_ORGANIC")), ...keys.filter(k => k.endsWith("_ORGANIC"))];
}
function fallPriceLabel(key, manyCrops) {
  const organic = key.endsWith("_ORGANIC");
  const crop = organic ? key.slice(0, -"_ORGANIC".length) : key;
  const name = crop.charAt(0) + crop.slice(1).toLowerCase();
  const text = (organic ? "Organic " + (manyCrops ? name.toLowerCase() + " " : "") : (manyCrops ? name + " " : "")) + "projected price";
  return text.charAt(0).toUpperCase() + text.slice(1);
}
// A year whose coverage the AIP has priced (acres or liability on a base line).
function fallYearReported(rows) {
  return (rows || []).some(r => {
    const p = String(r.plan || "").toUpperCase();
    if (p.includes("SCO") || p.includes("ECO") || p === "ARPI") return false;
    return r.acres != null || r.liability != null;
  });
}
// ⚠ THE CELLS OF A NOT-YET-REPORTED YEAR — pure, so the cross-year rule can be tested.
// Every VALUE comes from `rows` / `pp` / `scoEco` of `year`. `priorRows` supplies only the
// labelled reference line and nothing reads it for a value. A county that exists only in
// the prior year is not shown — this is the CY${year} page.
function fallYearCells({ year, rows, priorRows, pp, scoEco, past }) {
  const up = (v) => String(v || "").toUpperCase().trim();
  const isOverlay = (r) => { const p = up(r.plan); return p.includes("SCO") || p.includes("ECO") || p === "ARPI"; };
  const keyOf = (r) => up(r.county) + "|" + up(r.crop) + "|" + normalizePractice(r.practice);
  const cells = new Map();
  const cellFor = (r) => {
    const k = keyOf(r);
    if (!cells.has(k)) cells.set(k, { key: k, county: up(r.county), crop: up(r.crop), practice: normalizePractice(r.practice),
                                      row: null, prior: null, pp: [], endorsements: [] });
    return cells.get(k);
  };
  (rows || []).filter(r => !isOverlay(r)).forEach(r => { const c = cellFor(r); if (!c.row) c.row = r; });
  (pp || []).forEach(p => cellFor(p).pp.push(p));
  (scoEco || []).forEach(e => cellFor(e).endorsements.push(e));
  (priorRows || []).filter(r => !isOverlay(r)).forEach(r => { const c = cells.get(keyOf(r)); if (c && !c.prior) c.prior = r; });
  const covText = (r, ends) => {
    if (!r || r.coverageLevel == null) return null;
    const plans = [...new Set((ends || []).map(e => up(e.plan).replace(/-(RP|YP)$/, "")).filter(Boolean))];
    return Math.round(Number(r.coverageLevel) * 100) + "% " + (r.plan || "") +
           (r.unitStructure ? " · " + r.unitStructure : "") + (plans.length ? " + " + plans.join(" + ") : "");
  };
  return [...cells.values()].map(c => {
    const reported = !!(c.row && (c.row.liability != null || c.row.acres != null));
    const ppAcres = c.pp.reduce((s, p) => s + (Number(maxAcres(p)) || 0), 0);
    return {
      ...c, year, reported,
      approvedYield: reported && c.row.approvedYield != null ? Number(c.row.approvedYield) : null,
      workingYield: !reported && c.row && Number(c.row.approvedYield) > 0 ? Number(c.row.approvedYield) : null,
      // How the approved yield was resolved (BUG-096) — null on a row the database rule does not cover.
      aph: c.row && c.row.aph ? c.row.aph : null,
      coverage: covText(c.row, c.endorsements),
      endorsementsOnly: !c.row && c.endorsements.length
        ? [...new Set(c.endorsements.map(e => up(e.plan).replace(/-(RP|YP)$/, "")))].join(" + ") : null,
      liability: reported && c.row.liability != null ? Number(c.row.liability) : null,
      insuredAcres: reported && c.row.acres != null ? Number(c.row.acres) : null,
      premium: reported && c.row.netPremium != null ? Number(c.row.netPremium) : null,
      // A PAST year is not waiting for anything — a line with no acreage was simply never reported.
      guaranteeReason: reported ? null
        : (c.row ? (past ? "No CY" + year + " acreage was reported" : "No CY" + year + " acreage reported yet")
                 : (past ? "No CY" + year + " coverage line on the AIP file" : "No CY" + year + " coverage line on the AIP file yet")),
      ppEligibleAcres: c.pp.length ? ppAcres : null,
      priorYield: c.prior && c.prior.approvedYield != null ? Number(c.prior.approvedYield) : null,
      priorCoverage: covText(c.prior, null),
    };
  }).sort((a, b) => (a.crop + a.county + a.practice).localeCompare(b.crop + b.county + b.practice));
}

function FallYearSwitch({ years, value, current, onChange, isMobile }) {
  return (
    <div role="group" aria-label="Crop year"
         style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:8, margin:"4px 0 14px" }}>
      <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#9a7a40", textTransform:"uppercase", marginRight:4 }}>Crop year</span>
      {years.map(y => {
        const on = y === value;
        return (
          <button key={y} onClick={() => onChange(y)} aria-pressed={on}
            style={{ border:`1px solid ${on ? "#6b4f00" : "#e0d8c8"}`, background: on ? "#3a2800" : "#fff",
                     color: on ? "#fff" : "#6b4f00", borderRadius:18, padding: isMobile ? "5px 12px" : "6px 16px",
                     fontWeight:800, fontSize: isMobile ? 12 : 13, cursor:"pointer", display:"flex", alignItems:"baseline", gap:6 }}>
            CY{y}
            <span style={{ fontSize:10, fontWeight:600, color: on ? "#c8a840" : "#9a7a40" }}>
              {y === current ? "current" : y < current ? "prior year" : "next"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// The crop-year price line. Label and detail follow the ECONOMIC state; the machine
// provenance line is admin-only (see rmaPriceDisplay).
function FallYearPrice({ crop, year, isMobile, align, label }) {
  const r = resolveRmaPrice(String(crop || "").toUpperCase(), year, "projected");
  const d = rmaPriceDisplay(r, "projected");
  const tone = d.tone === "conflict" ? "#a8321e" : d.tone === "final" ? "#3a2800" : "#9a7a40";
  return (
    <div style={{ textAlign: align || (isMobile ? "left" : "right") }}>
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#9a7a40", textTransform:"uppercase" }}>{label || "Projected price"}</div>
      <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize: isMobile ? 22 : 26, color: tone, lineHeight:1.15 }}>
        {r.price != null ? fmtD(rmaPriceCents(r.price) / 100) : "—"}
      </div>
      <div title={d.detail || undefined}
           style={{ fontSize:12, fontWeight:700, color: tone, cursor: d.detail ? "help" : "default" }}>
        {d.label}{d.detail ? <span style={{ marginLeft:5, color:"#b8a070", fontWeight:600 }}>ⓘ</span> : null}
      </div>
      {d.detail && isMobile && <div style={{ fontSize:11, color:"#9a7a40", marginTop:1 }}>{d.detail}</div>}
      {isCurrentUserAdmin() && d.machine && (
        <div style={{ fontSize:10, color:"#b0a080", marginTop:2 }}>{d.machine}</div>
      )}
    </div>
  );
}

// ⚠ A CROP YEAR THE AIP HAS NOT PRICED YET, or one no price map on this card can prove.
// Kevin, 2026-09-17, putting the two years side by side: "I want the UI to match, make 2027
// match 2026." So it is drawn with the priced year's own furniture — the header and price, the
// dark policy-total banner, and one table per county with the same eleven columns and the
// SCO/ECO supplemental rows — and every cell the year cannot yet support says so.
//
// It still computes NO money. The only dollars on it are the AIP's own, copied from that year's
// rows; the guarantee columns stay Pending until the priced table takes over. A banner total
// appears only when EVERY line carries the figure — a partial sum must not sit in a total's
// position. The prior year appears only as a grey reference line inside a cell, labelled with
// its own year, and nothing reads it for a value.
function FallYearSummary({ year, priorYear, rows, priorRows, pp, scoEco, isMobile, past }) {
  const cells = fallYearCells({ year, rows, priorRows, pp, scoEco, past });
  const pending = past ? "Not reported" : "Pending";
  if (!cells.length) {
    return (
      <Card style={{ textAlign:"center", padding:"32px 24px", border:"1px dashed #d8d0c0" }}>
        <div style={{ fontWeight:800, fontSize:15, color:"#3a2800", marginBottom:6 }}>No CY{year} fall coverage on file</div>
        <div style={{ color:"#9a7a40", fontSize:13 }}>Nothing for CY{year} has arrived from the AIP for this farm.</div>
      </Card>
    );
  }
  const up = (v) => String(v || "").toUpperCase();
  const crops = [...new Set(cells.map(c => c.crop))];
  const counties = [...new Set(cells.map(c => c.county))].sort();
  const pendEl = (small) => (
    <span style={{ color:"#b8a070", fontFamily:"sans-serif", fontWeight:600, fontSize: small ? 11 : 12 }}>{pending}</span>
  );
  const ref = (text, title) => (
    <div title={title} style={{ fontSize:10, color:"#9a8a6a", fontWeight:500, fontFamily:"sans-serif", whiteSpace:"nowrap", marginTop:2, cursor: title ? "help" : "default" }}>{text}</div>
  );
  const endNet = (e) => e.netPremium != null ? Number(e.netPremium)
    : (e.totalPremium != null && e.subsidy != null) ? Number(e.totalPremium) - Number(e.subsidy) : null;
  // ALL-OR-NOTHING: every county line must be on the file and carry the figure.
  const everyLine = cells.every(c => c.row);
  const sumAll = (vals) => (everyLine && vals.length && vals.every(v => v != null)) ? vals.reduce((t, v) => t + Number(v), 0) : null;
  const totLiab  = sumAll(cells.map(c => c.liability));
  const totAcres = sumAll(cells.map(c => c.insuredAcres));
  const totPrem  = sumAll([...cells.map(c => c.premium), ...cells.flatMap(c => c.endorsements.map(endNet))]);
  const bannerLabel = { fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#c8a840", textTransform:"uppercase", marginBottom:4 };
  const bannerPending = <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:22, color:"#c8a840" }}>{pending}</div>;
  const td = { padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", verticalAlign:"top" };
  const subTd = { padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12 };
  const bands = scoEcoBands(year);
  const mid = String(Math.round(bands.scoTop * 100));
  return (
    <div>
      <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:14 }}>
        <div>
          <div style={{ fontWeight:800, fontSize: isMobile ? 18 : 20, color:"#1a2e1a" }}>{crops.join(" · ")} · CY{year}</div>
          <div style={{ fontSize:12, color:"#9a7a40", marginTop:2 }}>
            {past ? "Prior crop year — no acreage was reported"
              : cells.some(c => c.reported) ? "Current crop year — coverage as the AIP has reported it, not yet priced"
              : "Current crop year — the AIP has not priced this policy yet"}
          </div>
        </div>
        {fallPriceKeys(cells.map(c => ({ crop: c.crop, practice: c.practice, row: c.row }))).map(k => (
          <FallYearPrice key={k} crop={k} year={year} isMobile={isMobile} label={fallPriceLabel(k, crops.length > 1)} />
        ))}
      </div>

      {/* ── POLICY TOTAL BANNER — the priced year's, with the year's own figures or Pending ── */}
      <Card style={{ marginBottom:20, background:"#2a1e00", border:"none" }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:32, alignItems:"center" }}>
          <div>
            <div style={bannerLabel}>Policy Total{counties.length > 1 ? ` — ${counties.length} Counties` : ""}</div>
            <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:26, color: totLiab != null ? "#fff" : "#c8a840" }}>
              {totLiab != null ? fmtD0(totLiab) : pending}
            </div>
            <div style={{ fontSize:11, color:"#9a7a40", marginTop:2 }}>
              {totLiab != null ? "liability as reported by the AIP" : "total revenue guarantee"}
            </div>
          </div>
          <div style={{ borderLeft:"1px solid #6b4f0044", paddingLeft:32 }}>
            <div style={bannerLabel}>Bu. Guarantee</div>
            {bannerPending}
          </div>
          <div style={{ borderLeft:"1px solid #6b4f0044", paddingLeft:32 }}>
            <div style={bannerLabel}>Total Insured Acres</div>
            {totAcres != null
              ? <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:22, color:"#fff" }}>{fmt(totAcres, 2)} ac</div>
              : bannerPending}
          </div>
          <div style={{ borderLeft:"1px solid #6b4f0044", paddingLeft:32 }}>
            <div style={bannerLabel}>Farmer Premium</div>
            {totPrem != null
              ? <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:22, color:"#fff" }}>{fmtD0(totPrem)}</div>
              : bannerPending}
          </div>
        </div>
      </Card>

      {counties.map(county => {
        const countyCells = cells.filter(c => c.county === county);
        const units = [...new Set(countyCells.map(c => yieldUnit(c.row || { crop: c.crop })))];
        const u = units.length === 1 ? units[0] : null;
        const heads = ["Crop","Plan","Coverage","Appr. Yield", u ? `Price / ${u}` : "Price",
          "Guar. / Acre","Acres", u ? (u === "bu" ? "Bu Guar" : `${u} Guar`) : "Prod. Guar",
          "Liability","Gross Premium","Farmer Premium"];
        return (
          <Card key={county} style={{ marginBottom:16 }}>
            <SectionTitle>{county} County</SectionTitle>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ background:"#f8f4ec" }}>
                    {heads.map(h => (
                      <th key={h} style={{ padding:"8px 12px", textAlign:h==="Crop"?"left":"right", fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#9a7a40", textTransform:"uppercase", borderBottom:"1px solid #e8e0d0", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {countyCells.map(c => {
                    const r = c.row;
                    const unit = yieldUnit(r || { crop: c.crop });
                    const color = CROP_COLOR[c.crop] || "#6b4f00";
                    const typeName = (r && r.type) || (c.endorsements.find(e => e.type) || {}).type || "";
                    // THE YEAR'S OWN PRICE, from the one ladder. A CERTIFIED organic line reads the
                    // organic election (CY2027 wheat: PM-26-056, $14.22) and never borrows the
                    // conventional price; with no organic figure it reads Pending. TRANSITIONAL
                    // organic (714 / 712) is priced conventional by RMA, so it reads the crop's price.
                    const organic = c.practice === "Organic";
                    const pr = resolveRmaPrice(fallPriceKey(c.crop, c.practice, r), year, "projected");
                    const pd = rmaPriceDisplay(pr, "projected");
                    const ends = [
                      ...c.endorsements.filter(e => { const p = up(e.plan); return p.includes("SCO") || p === "ARPI"; }),
                      ...c.endorsements.filter(e => up(e.plan).includes("ECO")),
                    ];
                    const fmtY = (v) => `${fmt(v, (v < 20 || !Number.isInteger(+v)) ? 1 : 0)} ${unit}`;   // a blended APH (98.7) keeps its tenth
                    return (
                      <React.Fragment key={c.key}>
                        <tr style={{ borderBottom: ends.length ? "none" : "1px solid #f0ece4" }}>
                          <td style={{ padding:"10px 12px", verticalAlign:"top" }}>
                            <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                              <div style={{ width:10, height:10, borderRadius:"50%", background:color, flexShrink:0 }} />
                              <span style={{ fontWeight:700, color:"#1a2e1a" }}>{c.crop}{typeName ? ` · ${typeName}` : ""}{organic ? <span style={{fontSize:10,color:"#5a7a5a",marginLeft:4,fontWeight:600}}>· Organic</span> : ""}</span>
                            </div>
                          </td>
                          <td style={{ padding:"10px 12px", textAlign:"right", verticalAlign:"top" }}>
                            {r && r.plan ? <Pill color={color}>{r.plan}</Pill> : pendEl()}
                          </td>
                          <td style={{ ...td, fontWeight:700, color:"#6b4f00" }}>
                            <div>{r && r.coverageLevel != null ? covLevelText(r.coverageLevel, r.coverageType) : pendEl()}</div>
                            {c.priorCoverage && ref(`${priorYear}: ${c.priorCoverage}`)}
                          </td>
                          <td style={td}>
                            <div>{c.approvedYield != null ? fmtY(c.approvedYield) : pendEl()}</div>
                            {c.priorYield != null && ref(`${priorYear}: ${fmtY(c.priorYield)}`)}
                            {(() => {
                              // BUG-096: a blended figure is labelled as one, and an unresolved one is
                              // never replaced by the summary row. "AIP working" stays only where the
                              // figure really is the AIP's single number.
                              const lab = fallAphLabel(c.aph, year);
                              if (c.aph && (c.aph.source === "resolving" || c.aph.source === "unreadable")) return ref(lab.text, lab.title);
                              if (c.workingYield == null) return c.approvedYield != null && lab ? ref(lab.text, lab.title) : null;
                              if (lab) return ref(`${c.aph.source === "weighted" ? "Across databases" : "AIP summary"}: ${fmtY(c.workingYield)}`, `${lab.text}. ${lab.title}`);
                              return ref(
                                past ? `APH on file: ${fmtY(c.workingYield)}` : `AIP working: ${fmtY(c.workingYield)}`,
                                past ? `The AIP's CY${year} file carries ${fmtY(c.workingYield)} — no acreage was reported against it`
                                     : `AIP working figure for CY${year}: ${fmtY(c.workingYield)} — not yet an approved yield`);
                            })()}
                          </td>
                          <td style={td}>
                            {pd.tone === "conflict" ? <span style={{ color:"#a8321e", fontWeight:700, fontFamily:"sans-serif", fontSize:12 }}>Under review</span>
                              : pr.price != null ? <div>{fmtD(rmaPriceCents(pr.price) / 100)}/{unit}</div>
                              : pendEl()}
                          </td>
                          <td style={td}>{pendEl()}</td>
                          <td style={{ ...td, fontWeight:700 }}>{c.insuredAcres != null ? fmt(c.insuredAcres, 2) : pendEl()}</td>
                          <td style={td}>{pendEl()}</td>
                          <td style={td}>{c.liability != null ? <span style={{ fontWeight:700, color:"#3a2800" }}>{fmtD0(c.liability)}</span> : pendEl()}</td>
                          <td style={td}>{c.reported && r.totalPremium != null ? <span style={{ fontWeight:700, color:"#3a2800" }}>{fmtD0(Number(r.totalPremium))}</span> : pendEl()}</td>
                          <td style={td}>
                            {c.premium != null
                              ? <div style={{ display:"inline-block", background:CROP_BG[c.crop]||"#f8f4ec", border:`1px solid ${color}44`, borderRadius:6, padding:"3px 9px" }}>
                                  <span style={{ fontWeight:800, color }}>{fmtD0(c.premium)}</span>
                                </div>
                              : pendEl()}
                          </td>
                        </tr>
                        {/* SCO/ECO sub-rows — the same rows the priced table draws, on this YEAR's lattice */}
                        {ends.map((e, si) => {
                          const isSCO = !up(e.plan).includes("ECO");
                          const sColor = isSCO ? "#7a5a1a" : "#4a6a8a";
                          const baseCov = r && r.coverageLevel != null ? (Number(r.coverageLevel) * 100).toFixed(0) : null;
                          const ecoCov = e.coverageLevel ? (Number(e.coverageLevel) * 100).toFixed(0) : String(Math.round(bands.ecoTop * 100));
                          const band = isSCO ? (baseCov ? `${baseCov}–${mid}%` : `to ${mid}%`) : `${mid}–${ecoCov}%`;
                          // ⚠ BEFORE ACREAGE IS REPORTED THE AIP WRITES THESE LINES AT $0, and "$0" reads as a
                          // fact — no premium, no coverage. It is a placeholder, so an endorsement's own
                          // dollars show only once its county line is reported.
                          const sAcres = c.reported && e.acres != null && Number(e.acres) > 0 ? Number(e.acres) : null;
                          const sLiab = c.reported && e.liability != null ? Number(e.liability) : null;
                          const sGross = c.reported && e.totalPremium != null ? Number(e.totalPremium) : null;
                          const sNet = c.reported ? endNet(e) : null;
                          const blank = <span style={{ color:"#ccc", fontSize:11 }}>—</span>;
                          return (
                            <tr key={`se-${si}`} style={{ borderBottom: si === ends.length - 1 ? "1px solid #f0ece4" : "none", background: isSCO ? "#fdf8ee" : "#f0f5fa" }}>
                              <td style={{ padding:"6px 12px 6px 24px", color:"#888", fontSize:11 }}>
                                <span style={{ marginRight:5, color:"#ccc" }}>└</span>
                                <span style={{ color:sColor, fontStyle:"italic", fontWeight:600 }}>supplemental</span>
                              </td>
                              <td style={{ padding:"6px 12px", textAlign:"right" }}><Pill color={sColor}>{e.plan === "ARPI" ? "SCO-RP" : e.plan}</Pill></td>
                              <td style={{ ...subTd, fontSize:11, color:sColor, fontWeight:700 }}>{band}</td>
                              <td style={{ padding:"6px 12px", textAlign:"right" }}>{blank}</td>
                              <td style={{ padding:"6px 12px", textAlign:"right" }}>{blank}</td>
                              <td style={subTd}>{sLiab != null && sAcres != null ? <span style={{ fontWeight:700, color:sColor }}>{fmtD(sLiab / sAcres)}<span style={{fontWeight:400,color:"#aaa",fontSize:10,marginLeft:2}}>/ac</span></span> : pendEl(true)}</td>
                              <td style={{ ...subTd, color:"#666" }}>{sAcres != null ? fmt(sAcres, 2) : pendEl(true)}</td>
                              <td style={{ padding:"6px 12px", textAlign:"right" }}>{blank}</td>
                              <td style={subTd}>{sLiab != null ? <span style={{ fontWeight:700, color:sColor }}>{fmtD0(sLiab)}</span> : pendEl(true)}</td>
                              <td style={subTd}>{sGross != null ? <span style={{ fontWeight:700, color:sColor }}>{fmtD0(sGross)}</span> : pendEl(true)}</td>
                              <td style={subTd}>{sNet != null ? <span style={{ fontWeight:700, color:sColor }}>{fmtD0(sNet)}</span> : pendEl(true)}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
      {priorYear != null && !past && cells.some(c => c.priorYield != null || c.priorCoverage) && (
        <div style={{ fontSize:12, color:"#9a7a40", padding:"4px 2px 12px" }}>
          Grey {priorYear} figures are last year's policy, shown for reference — nothing on this page is calculated from them. CY{priorYear} is one click away above.
        </div>
      )}
    </div>
  );
}

function FallView({ farmer, prices: cardPrices, isMobile, actions, priceMapForYear = null, customerView = false }) {
  const [tab, setTab] = useState("revenue");
  
  // ── WHICH CROP YEAR ─────────────────────────────────────────────────────
  // A farmer object built before the per-year rows existed (a demo or seed farm)
  // has no map and keeps the old single-list behaviour exactly.
  const fallYears   = farmer.approvedYieldsByYear ? fallYearsOnFile(farmer) : [];
  const openYear    = defaultFallYear(fallYears, new Date().toISOString().slice(0, 10));
  // ⚠ A year pick belongs to the farm it was made on. Held bare, picking CY2026 on one
  // farmer would carry into the next farmer opened in the same view.
  const [picked, setPicked] = useState(null);
  const pickedYear  = picked && picked.farmerId === farmer.id ? picked.year : null;
  const setPickedYear = (y) => setPicked({ farmerId: farmer.id, year: y });
  // BUG-096 — the approved yield every surface below prints is the wheat QUOTE's, resolved per
  // APH database (src/wheat/fallCardAph.js), never the stored summary row: Clark Five
  // Huntingdon CY2027 read "AIP working: 75.0" here while the quote and the printed quote read 98.7.
  const fallAphState = useFallWheatUnits(farmer);
  const viewYear    = fallYears.length ? (fallYears.includes(pickedYear) ? pickedYear : openYear) : null;
  const priorYear   = viewYear != null ? (fallYears.filter(y => y < viewYear)[0] ?? null) : null;
  const yearRows    = (y) => resolveFallRows(((farmer.approvedYieldsByYear || {})[y] || []), { year: y, units: unitsFor(fallAphState, farmer.id), status: statusFor(fallAphState, farmer.id) });
  // ⚠ ASK FOR THE VIEW YEAR'S MAP; NEVER INHERIT THE CARD'S. `cardPrices` is the map of
  // whichever card was clicked, and the detail view merges its siblings' rows under that
  // card's year — so on production Palladino & Carley's CY2025 PRF card priced his CY2026
  // and CY2027 wheat at CY2025's $6.06, and printed $195.44/ac from a CY2027 APH. The
  // Dashboard's _pricesFor tags the map with the year it really describes, so a year it
  // cannot describe is refused below rather than borrowed.
  const prices      = viewYear != null && priceMapForYear ? priceMapForYear(viewYear) : cardPrices;
  const priceYear   = viewYear ?? farmer.cropYear;
  const ayForYear   = viewYear != null ? yearRows(viewYear) : (farmer.approvedYields || []);
  // ⚠ THE PRICE MAP MUST BE THIS YEAR'S. `prices` is tagged with the year it actually
  // describes; a year view it does not describe never reaches the priced table.
  const pricesForYear = viewYear == null || (prices && prices._cropYear === viewYear);

  const yields  = ayForYear.filter(a => FALL_CROPS.includes(a.crop));
  // Merge scoEcoEndorsements into dedupedYields so SCO/ECO sub-rows render correctly
  const scoEco  = (farmer.scoEcoEndorsements||[]).filter(e => FALL_CROPS.includes((e.crop||'').toUpperCase())
    && (viewYear == null || Number(e.cropYear) === viewYear));
  // Deduplicate — keep only the first row per county+crop+plan+coverageLevel combination
  const normT = t => { const s = (t||"").toUpperCase().trim(); return s.includes("/") ? s.split("/").pop().trim() : s; };
  const seenFall = new Set();
  const dedupedYields = [...yields, ...scoEco].filter(a => {
    const key = `${a.county}|${a.crop}|${normT(a.type)}|${a.plan}|${a.coverageLevel}|${normalizePractice(a.practice)}`;
    if (seenFall.has(key)) return false;
    seenFall.add(key); return true;
  });
  const counties = [...new Set(dedupedYields.map(a => a.county))];
  const wheatPP  = (viewYear != null ? ((farmer.ppEligibilityByYear || {})[viewYear] || []) : (farmer.ppEligibility || []))
    .filter(p => FALL_CROPS.includes((p.crop||"").toUpperCase()));
  const showYearSummary = viewYear != null && !(pricesForYear && fallYearReported(yields) && fallYearPriceable(yields, prices));
  const hasPP    = wheatPP.length > 0;
  const hasAPH   = dedupedYields.length > 0;

  // Prevent-plant acres per (crop, county) — AUTHORITATIVE from the AIP M13 feed
  // (crop_coverage.pp_acres, written from P11 f[37]='P'). Same source SpringView
  // uses. Fall PP is rare (it takes a wet fall to stop wheat going in) and there
  // is none in the book today, but without this the fall revenue guarantee would
  // silently include the PP portion of RMA's blended liability the first time it
  // happens. NO heuristics — never infer PP from missing planting dates.
  const [ppByKey, setPpByKey] = useState({});
  // Uninsurable acres (crop_coverage.ui_acres) — acres carrying $0 liability ON
  // THIS CROP LINE, which the SOI counts outside its INSURED total. Already
  // excluded from `acres` by the sync, so they change no guarantee; carried here
  // so the card can show them and the page ties: total = insured + UI,
  // insured = planted + PP.
  //
  // These acres are NOT necessarily uncovered. Where the cause is prevent plant
  // beyond the crop's max eligible PP, the AIP ROLLS them onto another crop and
  // pays them there (Fire Creek 2026: 56.7 Ontario corn ac → 56.3 soybeans +
  // 0.4 dry beans, $12,612 + $73 of PP liability). The file gives no link
  // between the two sides, so nothing here infers it — see the label's tooltip.
  const [uiByKey, setUiByKey] = useState({});
  useEffect(() => {
    const pids = (farmer.allPolicyIds || farmer._allPolicyIds || []);
    if (!pids.length) { setPpByKey({}); return; }
    let alive = true;
    // uiByKey was declared here but never filled, so a fall crop's uninsurable
    // acres never reached the card the way a spring crop's do. Same fetch.
    sbFetch("crop_coverage", { select: PP_UI_SELECT,
      filters: [`policy_id=in.(${pids.join(",")})`, PP_UI_FILTER] })
      .then(rows => {
        if (!alive) return;
        const { pp, ui } = buildPpUiMaps(rows, c => FALL_CROPS.includes((c.crop||"").toUpperCase()));
        setPpByKey(pp); setUiByKey(ui);
      }).catch(() => {});
    return () => { alive = false; };
  }, [farmer.id, farmer.cropYear]);
  // Take the ROW — see ppKey. Wheat and triticale share type 'W', so the type +
  // practice halves of the key are what keep their prevent plant apart.
  const ppFor = (row) => ppByKey[ppKeyOfRow(row)] || 0;
  const uiFor = (row) => uiByKey[ppKeyOfRow(row)] || 0;
  // Per-unit detail for the "N units" drill-down (crop_coverage_units).
  // The VIEW year's units — the card's year would hand a CY2027 line CY2026's units.
  const unitsByKey = useCoverageUnits(farmer, viewYear);
  const [openUnits, setOpenUnits] = useState(() => new Set());
  const toggleUnits = (k) => setOpenUnits(prev => { const x = new Set(prev); x.has(k) ? x.delete(k) : x.add(k); return x; });

  const T = (active) => ({
    padding: isMobile ? "8px 10px" : "10px 22px", border:"none", background:"none", cursor:"pointer",
    borderBottom: active ? "3px solid #6b4f00" : "3px solid transparent",
    color: active ? "#3a2800" : "#9a7a40",
    fontWeight: active ? 800 : 500, fontSize: isMobile ? 12 : 14, marginBottom:-1,
    whiteSpace:"nowrap",
  });

  return (
    <div>
      {/* The APH banner describes the farm's collapsed rows, so on a crop year the AIP has not
          priced it would say "all crops have approved yields" over a page reading Pending. */}
      {!showYearSummary && <APHBanner farmer={farmer} fallOnly={true} />}

      {viewYear != null && fallYears.length > 1 && (
        <FallYearSwitch years={fallYears} value={viewYear} current={openYear} onChange={setPickedYear} isMobile={isMobile} />
      )}

      {/* Tab bar — wraps on mobile so labels stay readable */}
      <div style={{ display:"flex", flexWrap:"wrap", borderBottom:"1px solid #e8e0d0", marginBottom:20 }}>
        <button style={T(tab==="revenue")} onClick={()=>setTab("revenue")}>
          📋 {isMobile ? "Revenue" : "Revenue Guarantee"}
        </button>
        <button style={T(tab==="pp")} onClick={()=>setTab("pp")}>
          🌧️ {isMobile ? "PP" : "Prevent Plant"}
          {hasPP && <span style={{ marginLeft:6, fontSize:11, background:"#f4f0e8", border:"1px solid #c8a84044", borderRadius:10, padding:"1px 7px", color:"#6b4f00", fontWeight:700 }}>{wheatPP.length} crops</span>}
          {(() => { const y = ppRowsYear(wheatPP);
            return y ? <span style={{ marginLeft:5, fontSize:10, color:"#6b4f00", fontWeight:700 }}>CY{y}</span> : null; })()}
        </button>
        {actions}
        <button style={T(tab==="docs")} onClick={()=>setTab("docs")}>
          📄 {isMobile ? "578" : "578 Documents"}
        </button>
        <button style={T(tab==="aph")} onClick={()=>setTab("aph")}>
          📊 APH{isMobile ? "" : " History"}
        </button>
        <button style={T(tab==="claims")} onClick={()=>setTab("claims")}>
          📋 Claims{isMobile ? "" : " History"}
        </button>
      </div>

      {/* ── APH HISTORY TAB ── */}
      {tab === "aph" && (
        <FarmerAPHHistory farmer={farmer} />
      )}

      {/* ── CLAIMS HISTORY TAB ── (fall crops only — wheat / barley / triticale / rye) */}
      {tab === "claims" && (
        <FarmerClaimsHistory farmer={farmer} productFilter="crop" />
      )}

      {/* ── 578 DOCUMENTS TAB ── */}
      {/* A farmer sees their own stored 578s here (Kevin, 2026-11-02); only the
          File Management page is staff-only. customerView drops agency workflow. */}
      {tab === "docs" && (
        <Farmer578Documents farmer={farmer} season="fall" isMobile={isMobile} customerView={customerView} />
      )}

      {/* ── REVENUE GUARANTEE TAB ── */}
      {tab === "revenue" && (
        <div>
          {showYearSummary ? (
            <FallYearSummary year={viewYear} priorYear={priorYear}
              rows={yields} priorRows={priorYear != null ? yearRows(priorYear).filter(a => FALL_CROPS.includes(a.crop)) : []}
              pp={wheatPP} scoEco={scoEco} isMobile={isMobile} past={viewYear < openYear} />
          ) : !hasAPH ? (
            <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #d8d0c0" }}>
              <div style={{ fontSize:32, marginBottom:10 }}>🌾</div>
              <div style={{ fontWeight:800, fontSize:15, color:"#3a2800", marginBottom:6 }}>No fall crop APH on file</div>
              <div style={{ color:"#9a7a40", fontSize:13 }}>Upload a Wheat APH Database or Schedule of Insurance to populate fall crop data.</div>
            </Card>
          ) : (
            <>
              {viewYear != null && (
                <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:14 }}>
                  <div>
                    <div style={{ fontWeight:800, fontSize: isMobile ? 18 : 20, color:"#1a2e1a" }}>
                      {[...new Set(yields.map(r => (r.crop || "").toUpperCase()))].join(" · ")} · CY{viewYear}
                    </div>
                    <div style={{ fontSize:12, color:"#9a7a40", marginTop:2 }}>
                      {viewYear === openYear ? "Current crop year" : viewYear < openYear ? "Prior crop year — kept as reported" : "Next crop year"}
                    </div>
                  </div>
                  {/* ⚠ A HEADER MAY NOT DISAGREE WITH THE TABLE UNDER IT. The price line is shown
                      only for a crop whose table price IS the ladder's own published cent. CY2026
                      barley is the case that found it: the table prices it from a constant of
                      $5.45 while RMA's ADM states $4.20 for every NY and PA barley type — a
                      pre-existing CY2026 value reported, not changed, by the CY2027 wheat work. */}
                  {fallPriceKeys(yields.map(r => ({ crop: r.crop, practice: r.practice, row: r })))
                    .filter(c => rmaSameCent(prices && prices[c], resolveRmaPrice(c, viewYear, "projected").price))
                    .map((c, _i, shown) => (
                      <FallYearPrice key={c} crop={c} year={viewYear} isMobile={isMobile}
                        label={fallPriceLabel(c, new Set(yields.map(r => (r.crop || "").toUpperCase())).size > 1)} />
                    ))}
                </div>
              )}
              {/* ── POLICY TOTAL BANNER ── */}
              {(() => {
                const baseYields = dedupedYields.filter(r => r.plan && !r.plan.includes('ECO') && !r.plan.includes('SCO'));
                // Planted acres only — prevent-plant acres are on the Prevent Plant tab,
                // and the guarantee below is split to match (see plantedRevenueGuarantee).
                const totalAcres = baseYields.reduce((s,r) => {
                  const pp = ppFor(r);
                  return s + (r.acres != null ? (pp > 0 ? Math.max(0, Number(r.acres) - pp) : Number(r.acres)) : 0);
                }, 0);
                // Bushel guarantee is per-crop — wheat bushels and triticale bushels
                // are different commodities and must NOT be summed into one number.
                // Sum across counties within a crop (same grain → additive), keep
                // crops separate. Sorted largest-first for stable display.
                // M13 liability is authoritative (acre-weighted) — use it whenever
                // present, exactly like the spring summary + the table below. Only
                // fall back to APH × price × cov × acres when there's no liability
                // yet (acreage not reported). Bushel guarantee = liability ÷ price
                // (matches the SOI "Marketable Guarantee"); APH × cov × acres is the
                // fallback. Previously this banner ALWAYS estimated, so a reported
                // wheat policy showed e.g. $43,725 instead of the M13 $43,351.
                const buByCrop = (() => {
                  const m = {};
                  baseYields.forEach(r => {
                    const c = (r.crop || '').toUpperCase();
                    const { price, unit } = getCropPriceAndUnit(r, prices, priceYear);
                    // pePct = price-election fraction (1.0 normally, 0.55 for CAT — e.g.
                    // CAT barley). Without it CAT bushel guarantees read ~45% low.
                    const pePct = Number(r.priceElectionPct ?? r.price_election_pct) > 0 ? Number(r.priceElectionPct ?? r.price_election_pct) : 1;
                    const effPrice = (unit === "lbs" ? price * 0.01 : price) * pePct;
                    const pp = ppFor(r);
                    if (r.liability != null && effPrice > 0) { m[c] = (m[c] || 0) + (plantedSplit(r, pp, unitsByKey).liability || 0) / effPrice; return; }
                    if (r.acres == null || !r.approvedYield || !r.coverageLevel) return;
                    m[c] = (m[c] || 0) + (r.approvedYield * r.coverageLevel * Math.max(0, Number(r.acres) - pp));
                  });
                  return Object.entries(m).filter(([,bu]) => bu > 0).sort((a,b) => b[1]-a[1]);
                })();
                const totalRevGuar = baseYields.reduce((s,r) => {
                  const pp = ppFor(r);
                  if (r.liability != null) return s + (plantedSplit(r, pp, unitsByKey).liability || 0);
                  if (r.acres == null || !r.approvedYield || !r.coverageLevel) return s;
                  const { price } = getCropPriceAndUnit(r, prices, priceYear);
                  const pePct = Number(r.priceElectionPct ?? r.price_election_pct) > 0 ? Number(r.priceElectionPct ?? r.price_election_pct) : 1;
                  return s + (r.approvedYield * price * pePct * r.coverageLevel * Math.max(0, Number(r.acres) - pp));
                }, 0);
                const totalFarmerPrem = (() => {
                  const baseNet = baseYields.reduce((s,r) => s + (Number(r.netPremium)||0), 0);
                  const scoEcoNet = dedupedYields.filter(r => r.plan && (r.plan.includes('SCO') || r.plan.includes('ECO')))
                    .reduce((s,r) => s + (Number(r.netPremium)||0), 0);
                  return baseNet + scoEcoNet;
                })();
                if (totalAcres === 0) return null;
                // Same rule as SpringView — see src/coverageDollars.js. A fall
                // coverage the AIP has reported acres for and no dollars on renders
                // an APH-modelled guarantee that is indistinguishable from a priced
                // one, and that is what has to be said out loud.
                const silentNote = silentCoverageNote(baseYields);
                return (
                  <>
                  <Card style={{ marginBottom:20, background:"#2a1e00", border:"none" }}>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:32, alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#c8a840", textTransform:"uppercase", marginBottom:4 }}>
                          Policy Total{counties.length > 1 ? ` — ${counties.length} Counties` : ''}
                        </div>
                        <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:26, color:"#fff" }}>
                          {fmtD0(totalRevGuar)}
                        </div>
                        <div style={{ fontSize:11, color:"#9a7a40", marginTop:2 }}>total revenue guarantee</div>
                      </div>
                      {buByCrop.length > 0 && (
                        <div style={{ borderLeft:"1px solid #6b4f0044", paddingLeft:32 }}>
                          <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#c8a840", textTransform:"uppercase", marginBottom:4 }}>
                            {buByCrop.length > 1 ? 'Bu. Guarantee by Crop' : 'Bu. Guarantee'}
                          </div>
                          {buByCrop.length === 1 ? (
                            <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:22, color:"#fff" }}>
                              {fmt(buByCrop[0][1], 0)} bu
                            </div>
                          ) : (
                            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                              {buByCrop.map(([crop, bu]) => (
                                <div key={crop} style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                                  <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:18, color:"#fff", minWidth:78, textAlign:"right" }}>{fmt(bu, 0)} bu</span>
                                  <span style={{ fontSize:11, color:"#c8a840", fontWeight:700, textTransform:"capitalize" }}>{crop.toLowerCase()}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ borderLeft:"1px solid #6b4f0044", paddingLeft:32 }}>
                        <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#c8a840", textTransform:"uppercase", marginBottom:4 }}>Total Insured Acres</div>
                        <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:22, color:"#fff" }}>
                          {fmt(totalAcres, 2)} ac
                        </div>
                      </div>
                      {totalFarmerPrem > 0 && (
                        <div style={{ borderLeft:"1px solid #6b4f0044", paddingLeft:32 }}>
                          <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.12em", color:"#c8a840", textTransform:"uppercase", marginBottom:4 }}>Farmer Premium</div>
                          <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:22, color:"#fff" }}>
                            {fmtD0(totalFarmerPrem)}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                  {silentNote && (
                    <Card style={{ marginBottom:16, background:"#fdf8ec", border:"1px solid #e0cf9a" }}>
                      <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                        <span style={{ fontSize:15, lineHeight:"18px" }}>⚠</span>
                        <div>
                          <div style={{ fontWeight:800, fontSize:13, color:"#7a5c10", marginBottom:4 }}>
                            {silentNote.headline}
                          </div>
                          <div style={{ fontSize:12, color:"#6b5a2a", lineHeight:1.5 }}>{silentNote.detail}</div>
                        </div>
                      </div>
                    </Card>
                  )}
                  </>
                );
              })()}
              {counties.map(county => {
                const rows = dedupedYields.filter(a => a.county === county);
                const rpRows  = rows.filter(r => !r.plan || (!r.plan.includes('SCO') && !r.plan.includes('ECO') && r.plan !== 'ARPI'));
                const scoRows = rows.filter(r => r.plan && (r.plan.includes('SCO') || r.plan === 'ARPI'));
                const ecoRows = rows.filter(r => r.plan && r.plan.includes('ECO'));
                return (
                  <Card key={county} style={{ marginBottom:16 }}>
                    <SectionTitle>{county} County</SectionTitle>
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                        <thead>
                          <tr style={{ background:"#f8f4ec" }}>
                            {/* Price/guarantee headers follow the crop's own yield unit — fall crops
                                are almost always bu, but corn silage on a fall policy is tons. */}
                            {(() => {
                              const units = [...new Set(rpRows.map(r => yieldUnit(r)))];
                              const u = units.length === 1 ? units[0] : null;
                              return ["Crop","Plan","Coverage","Appr. Yield", u ? `Price / ${u}` : "Price",
                                "Guar. / Acre","Acres", u ? (u === "bu" ? "Bu Guar" : `${u} Guar`) : "Prod. Guar",
                                "Liability","Gross Premium","Farmer Premium"];
                            })().map(h => (
                              <th key={h} style={{ padding:"8px 12px", textAlign:h==="Crop"?"left":"right", fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#9a7a40", textTransform:"uppercase", borderBottom:"1px solid #e8e0d0", whiteSpace:"nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rpRows.map((r,i) => {
                            const { price, unit } = getCropPriceAndUnit(r, prices, priceYear);
                            const isSilage = unit === "ton";
                            // Revenue guarantee is on PLANTED acres only — split the blended RMA
                            // liability when this crop/county has prevent-plant acres, matching
                            // SpringView. PP guarantee belongs on the Prevent Plant tab.
                            const ppAcres = ppFor(r);
                            const uiAcres = uiFor(r);
                            const effPrice = unit === "lbs" ? price * 0.01 : price;
                            // Units passed in so the planted liability is SUMMED from the planted
                            // units when they're complete, not modelled at the PP factor.
                            // ONE lookup, shared with statedGuaranteeBu below, so the planted split and
                            // the stated guarantee can never disagree about which units belong here.
                            const _uRows     = unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)];
                            const _pv     = plantedView(r, ppAcres, r.approvedYield * effPrice * r.coverageLevel, _uRows);
                            const acres   = _pv.acres;
                            const liability = _pv.liability;
                            const gpa     = _pv.gpa;
                            // Bushel (production) guarantee via the shared guaranteeBushels() — liability
                            // ÷ price (acre-weighted, matches the SOI "Marketable Guarantee"); fallback =
                            // APH × coverage% × acres. Planted liability/acres (PP removed) passed in.
                            const buGuar  = guaranteeBushels(liability, effPrice, r.approvedYield, r.coverageLevel, acres, (r.priceElectionPct ?? r.price_election_pct), statedGuaranteeBu(_uRows));
                            const grossPrem   = r.totalPremium != null ? Number(r.totalPremium) : null;
                            const farmerPrem  = r.netPremium != null ? Number(r.netPremium)
                              : (grossPrem != null && r.subsidy != null) ? grossPrem - Number(r.subsidy)
                              : grossPrem;
                            const isLowYield = isSilage ? (r.approvedYield != null && r.approvedYield < 8) : (r.crop==="WHEAT" && r.approvedYield != null && r.approvedYield < 20);
                            const _supp = matchSupplementals(scoRows, ecoRows, r, rpRows);
                            const matchSCO = _supp.sco, matchECO = _supp.eco;
                            const hasSuppCov = _supp.any();
                            return (
                              <React.Fragment key={i}>
                                <tr style={{ borderBottom: hasSuppCov ? "none" : "1px solid #f0ece4" }}>
                                  <td style={{ padding:"10px 12px" }}>
                                    <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                                      <div style={{ width:10, height:10, borderRadius:"50%", background:CROP_COLOR[r.crop]||"#6b4f00", flexShrink:0 }} />
                                      <span style={{ fontWeight:700, color:"#1a2e1a" }}>{r.crop}{r.type ? ` · ${r.type}` : ""}{normalizePractice(r.practice)==="Organic" ? <span style={{fontSize:10,color:"#5a7a5a",marginLeft:4,fontWeight:600}}>· Organic</span> : ""}</span>
                                    </div>
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right" }}><Pill color={CROP_COLOR[r.crop]||"#6b4f00"}>{r.plan}</Pill></td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#6b4f00" }}>{covLevelText(r.coverageLevel, r.coverageType)}</td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                                    <div>
                                      {/* Low APHs (tons/acre on grapes, silage) need a decimal —
                                          at 8.7 a whole number is a 3% swing in the guarantee. */}
                                      <span>{fmt(r.approvedYield, r.approvedYield != null && (Math.abs(r.approvedYield) < 20 || !Number.isInteger(+r.approvedYield)) ? 1 : 0)} {unit}</span>
                                      {isLowYield && <span title="Unusually low yield — verify APH." style={{marginLeft:5,fontSize:10,color:"#c0392b",fontWeight:800,cursor:"help"}}>⚠️ low</span>}
                                      {(() => { const lab = fallAphLabel(r.aph, viewYear ?? farmer.cropYear); return lab
                                        ? <div style={{ fontSize: 10, color: "#9a7a3a", fontWeight: 600, fontFamily: "sans-serif" }} title={lab.title}>{lab.text}</div> : null; })()}
                                      {(() => {
                                        const _uk = cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode);
                                        // PLANTED units only. This row shows the planted guarantee, so
                                        // its acre-weighted APH must be weighted over planted acres —
                                        // including a PP unit weighted 203.3 over 577.7 insured acres
                                        // on a line reading 533.4 planted.
                                        const _us = plantedUnits(unitsByKey[_uk]);
                                        const _w  = weightedAph(_us);
                                        if (!(r.unitCount > 1) && _us.length < 2) return null;
                                        return (
                                          <div style={{ marginTop: 2 }}>
                                            <button onClick={() => toggleUnits(_uk)} disabled={!_us.length}
                                              title={_us.length ? "Show each unit's acres, APH and liability as insured"
                                                                : "Unit detail appears after the next sync"}
                                              style={{ fontSize: 10, color: "#9a7a3a", fontWeight: 600, fontFamily: "sans-serif",
                                                       background: "none", border: "none", padding: 0,
                                                       cursor: _us.length ? "pointer" : "default",
                                                       textDecoration: _us.length ? "underline dotted" : "none" }}>
                                              {_us.length || r.unitCount} unit{(_us.length || r.unitCount) === 1 ? "" : "s"}
                                              {_us.length ? (openUnits.has(_uk) ? " ▲" : " ▼") : ""}
                                            </button>
                                            {_w != null && Math.abs(_w - (r.approvedYield || 0)) >= 0.5 && (
                                              <div style={{ fontSize: 10, color: T.faint, fontWeight: 600 }}
                                                   title="Acre-weighted across the units — what RMA prices off.">
                                                {_w} wtd
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>{unit === "lbs" ? `${fmtD(price/100)}/lbs` : `${fmtD(price)}/${unit}`}</td>
                                  <td style={{ padding:"10px 12px", textAlign:"right" }}>
                                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:CROP_COLOR[r.crop]||"#6b4f00" }}>{fmtD(gpa)}</span>
                                    <span style={{ fontSize:10, color:"#aaa", marginLeft:3 }}>/ac</span>
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700 }}
                                    title={ppAcres > 0 ? `Planted acres — ${fmt(ppAcres,1)} prevent-plant ac excluded (see Prevent Plant tab). Revenue guarantee is on planted acres only.` : undefined}>
                                    {acres != null
                                      ? <span>{fmt(acres,2)}{ppAcres > 0 && <span style={{ fontSize:10, color:"#9a5a1a", fontWeight:700, marginLeft:3 }}>planted</span>}</span>
                                      : <span style={{ color:"#bbb" }}>—</span>}
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                                    <BuGuarCell derived={buGuar} unit={unit} color="#6b4f00"
                                      units={unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)]} />
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                                    {liability != null ? <span style={{ fontWeight:700, color:"#3a2800" }}>{fmtD0(liability)}</span> : <span style={{ color:"#bbb" }}>—</span>}
                                  </td>
                                  {/* See SpringView — premium is on insured acres, the column
                                      beside it is planted. */}
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}
                                      title={premiumBasisNote(r, ppAcres)}>
                                    {grossPrem != null ? <span style={{ fontWeight:700, color:"#3a2800" }}>{fmtD0(grossPrem)}</span> : <span style={{ color:"#bbb" }}>—</span>}
                                  </td>
                                  <td style={{ padding:"10px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}
                                      title={premiumBasisNote(r, ppAcres)}>
                                    {farmerPrem != null
                                      ? <div style={{ display:"inline-block", background:CROP_BG[r.crop]||"#f8f4ec", border:`1px solid ${CROP_COLOR[r.crop]||"#6b4f00"}44`, borderRadius:6, padding:"3px 9px" }}>
                                          <span style={{ fontWeight:800, color:CROP_COLOR[r.crop]||"#6b4f00" }}>{fmtD0(farmerPrem)}</span>
                                          {ppAcres > 0 && <span style={{ fontSize:10, color:"#9a5a1a", fontWeight:700, marginLeft:4 }}>incl. PP</span>}
                                        </div>
                                      : <span style={{ color:"#bbb" }}>—</span>}
                                  </td>
                                </tr>
                                {/* SCO/ECO sub-rows — aligned to same 10-column structure */}
                                {[...matchSCO.map(s=>({...s,_tag:'SCO'})), ...matchECO.map(s=>({...s,_tag:'ECO'}))].map((s, si, arr) => {
                                  const isLast = si === arr.length - 1;
                                  const isSCO = s._tag === 'SCO';
                                  const baseCov  = r.coverageLevel ? (r.coverageLevel*100).toFixed(0) : '75';
                                  const ecoCov   = s.coverageLevel ? (s.coverageLevel*100).toFixed(0) : '95';
                                  // The SCO top / ECO bottom is the CROP YEAR's own lattice (86 for
                                  // CY2026, 90 from CY2027) — never a typed 86 on a CY2027 line.
                                  const _bandYear = s.cropYear ?? viewYear ?? farmer.cropYear;
                                  const _mid     = _bandYear ? String(Math.round(scoEcoBands(_bandYear).scoTop * 100)) : '—';
                                  const bandLow  = isSCO ? baseCov : _mid;
                                  const bandHigh = isSCO ? _mid : ecoCov;
                                  const color    = isSCO ? '#7a5a1a' : '#4a6a8a';
                                  const bg       = isSCO ? '#fdf8ee' : '#f0f5fa';
                                  const sAcres   = s.acres != null && Number(s.acres) > 0 ? Number(s.acres) : null;
                                  const sLiab    = s.liability != null ? Number(s.liability) : null;
                                  const sGross   = s.totalPremium != null ? Number(s.totalPremium) : null;
                                  const sFarmer  = s.netPremium != null ? Number(s.netPremium)
                                    : (sGross != null && s.subsidy != null) ? sGross - Number(s.subsidy)
                                    : sGross;
                                  const guarPerAcre = (sLiab != null && sAcres != null) ? sLiab / sAcres : null;
                                  const pfLabel = s.protectionFactor != null
                                    ? ` @ ${(s.protectionFactor*100).toFixed(0)}%`
                                    : '';
                                  return (
                                    <tr key={`sco-eco-${si}`} style={{ borderBottom: isLast ? "1px solid #f0ece4" : "none", background: bg }}>
                                      {/* Crop */}
                                      <td style={{ padding:"6px 12px 6px 24px", color:"#888", fontSize:11 }}>
                                        <span style={{ marginRight:5, color:"#ccc" }}>└</span>
                                        <span style={{ color, fontStyle:"italic", fontWeight:600 }}>supplemental</span>
                                      </td>
                                      {/* Plan */}
                                      <td style={{ padding:"6px 12px", textAlign:"right" }}>
                                        <Pill color={color}>{s.plan === 'ARPI' ? 'SCO-RP' : s.plan}</Pill>
                                      </td>
                                      {/* Coverage — band + protection factor */}
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:11, color, fontWeight:700 }}>
                                        {bandLow}–{bandHigh}%{pfLabel}
                                      </td>
                                      {/* Appr. Yield — blank */}
                                      <td style={{ padding:"6px 12px", textAlign:"right", color:"#ccc", fontSize:11 }}>—</td>
                                      {/* Price/bu — blank */}
                                      <td style={{ padding:"6px 12px", textAlign:"right", color:"#ccc", fontSize:11 }}>—</td>
                                      {/* Guar./Acre — liability ÷ acres */}
                                      <td style={{ padding:"6px 12px", textAlign:"right" }}>
                                        {guarPerAcre != null
                                          ? <span style={{ fontVariantNumeric: "tabular-nums", fontSize:12, fontWeight:700, color }}>{fmtD(guarPerAcre)}<span style={{fontWeight:400,color:"#aaa",fontSize:10,marginLeft:2}}>/ac</span></span>
                                          : <span style={{ color:"#ccc", fontSize:11 }}>—</span>}
                                      </td>
                                      {/* Acres */}
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12, color:"#666" }}>
                                        {sAcres != null ? fmt(sAcres,2) : <span style={{color:"#ccc"}}>—</span>}
                                      </td>
                                      {/* Bu Guar — area-plan supplementals have no bushel guarantee */}
                                      <td style={{ padding:"6px 12px", textAlign:"right", color:"#ccc", fontSize:11 }}>—</td>
                                      {/* Liability */}
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12 }}>
                                        {sLiab != null ? <span style={{ fontWeight:700, color }}>{fmtD0(sLiab)}</span> : <span style={{color:"#ccc"}}>—</span>}
                                      </td>
                                      {/* Gross Premium */}
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12 }}>
                                        {sGross != null ? <span style={{ fontWeight:700, color }}>{fmtD0(sGross)}</span> : <span style={{color:"#ccc"}}>—</span>}
                                      </td>
                                      {/* Farmer Premium */}
                                      <td style={{ padding:"6px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12 }}>
                                        {sFarmer != null ? <span style={{ fontWeight:700, color }}>{fmtD0(sFarmer)}</span> : <span style={{color:"#ccc"}}>—</span>}
                                      </td>
                                    </tr>
                                  );
                                })}
                                {/* Units exactly as insured — opened from the unit count above. */}
                                {openUnits.has(cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)) && (
                                  <CoverageUnitRows
                                    units={unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)]}
                                    colSpan={11} yieldUnit={unit} />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                        {/* County total row */}
                        {(() => {
                          const allRows = [...rpRows, ...scoRows, ...ecoRows];
                          // Base totals are PLANTED-only (prevent-plant split out, matching the
                          // rows above). SCO/ECO supplementals keep their area-plan liability.
                          const totAcres  = rpRows.reduce((s,r) =>
                            s + (r.acres != null ? (plantedSplit(r, ppFor(r), unitsByKey).acres || 0) : 0), 0);
                          // No bushel total in the footer — wheat bu and triticale bu can't be
                          // summed, so a per-crop footer just restates the rows above it.
                          const totLiab   = rpRows.reduce((s,r) => s + (plantedSplit(r, ppFor(r), unitsByKey).liability || 0), 0)
                            + [...scoRows, ...ecoRows].reduce((s,r) => s + (r.liability ? Number(r.liability) : 0), 0);
                          const totGross  = allRows.reduce((s,r) => s + (r.totalPremium ? Number(r.totalPremium) : 0), 0);
                          const totFarmer = allRows.reduce((s,r) => {
                            if (r.netPremium != null) return s + Number(r.netPremium);
                            if (r.totalPremium != null && r.subsidy != null) return s + Number(r.totalPremium) - Number(r.subsidy);
                            return s + (r.totalPremium ? Number(r.totalPremium) : 0);
                          }, 0);
                          if (totAcres === 0 && totLiab === 0) return null;
                          return (
                            <tfoot>
                              <tr style={{ background:"#f4f0e8", borderTop:"2px solid #e0d8c8" }}>
                                <td colSpan={6} style={{ padding:"8px 12px", fontSize:11, fontWeight:800, color:"#6b4f00", textTransform:"uppercase", letterSpacing:"0.06em" }}>County Total</td>
                                <td style={{ padding:"8px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#3a2800" }}>{totAcres > 0 ? fmt(totAcres,2) : "—"}</td>
                                <td style={{ padding:"8px 12px" }} />
                                <td style={{ padding:"8px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#3a2800" }}>{totLiab > 0 ? fmtD0(totLiab) : "—"}</td>
                                <td style={{ padding:"8px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#3a2800" }}>{totGross > 0 ? fmtD0(totGross) : "—"}</td>
                                <td style={{ padding:"8px 12px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:800, color:"#6b4f00" }}>{totFarmer > 0 ? fmtD0(totFarmer) : "—"}</td>
                              </tr>
                            </tfoot>
                          );
                        })()}
                      </table>
                    </div>
                  </Card>
                );
              })}

              {/* Same rule as SpringView. Anchor's WHEAT policy is where his
                  $446 credit (M 416 + F 30) zeroed the bill — a separate policy
                  from his corn/soy, so it reconciles here and only here. */}
              <PolicyBillingPanel
                farmer={farmer}
                crops={[...new Set(yields.map(a => a.crop))]}
                tone={{ accent:"#6b4f00", bg:"#faf6ee", rule:"#e0d8c8" }} />
            </>
          )}
        </div>
      )}

      {/* ── PREVENT PLANT TAB ── */}
      {tab === "pp" && (
        <div>
          {!hasPP ? (
            <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #d8d0c0" }}>
              <div style={{ fontSize:32, marginBottom:10 }}>📋</div>
              <div style={{ fontWeight:800, fontSize:15, color:"#3a2800", marginBottom:6 }}>No Wheat PP Eligibility on file</div>
              <div style={{ color:"#9a7a40", fontSize:13 }}>Upload a PP Eligibility Report PDF to see wheat prevent plant eligibility and payment per acre.</div>
            </Card>
          ) : (
            [...new Set(wheatPP.map(p => p.county))].map(county => {
              const ppRows = wheatPP.filter(p => p.county === county);
              const tableRows = ppRows.map(pp => {
                const _ppCUp = (pp.county||"").toUpperCase().trim(), _ppCrUp = (pp.crop||"").toUpperCase(), _ppTUp = (pp.type||"").toUpperCase(), _ppPUp = (pp.practice||"").toUpperCase();
                const _ppTCode = normalizeCropType(pp.type);
                const _ayBase = ayForYear.filter(a => (a.county||"").toUpperCase().trim()===_ppCUp && (a.crop||"").toUpperCase()===_ppCrUp && (!_ppTUp || normalizeCropType(a.type)===_ppTCode));
                const ay = _ayBase.find(a=>normalizePractice(a.practice).toUpperCase()===normalizePractice(pp.practice).toUpperCase()) || _ayBase.find(a=>!(a.practice||"")&&!(pp.practice||"")) || _ayBase[0] || null;
                const isSilage   = normalizeCropType(pp.type) === "026";
                const isPotatoes = (pp.crop||"").toUpperCase() === "POTATOES";
                const isDryBeans = (pp.crop||"").toUpperCase() === "DRY BEANS";
                const price = isSilage ? (RMA_SILAGE_PRICES[priceYear] || RMA_SILAGE_PRICES[2026])
                  : isPotatoes ? (ay?.price || prices["POTATOES"] || 17.85)
                  : isDryBeans ? (() => {
                      // pp.type often empty on SOI imports — fall back to linked ay.type, then BTS.
                      const beanType = ((pp.type || ay?.type) || "").toUpperCase();
                      const isOrg = normalizePractice(pp.practice) === "Organic";
                      const typeKey = isOrg && beanType ? `${beanType}_ORGANIC` : beanType;
                      return (typeKey && prices[typeKey]) || prices["BTS"] || 0;
                    })()
                  : (prices[pp.crop] || 0);
                // The prevent-plant unit RMA actually keyed, if there is one: its
                // stated Acre Guarantee Quantity is the elected factor as a READING
                // rather than a model, and outranks the options string below it.
                const _ppUnits = ay ? unitsByKey[cuKey(ay.county, ay.crop, ay.plan, ay.typeCode, ay.practiceCode)] : null;
                const maxAc = maxAcres(pp);
                // The view year, not the card's: a CY2027 row asks for CY2027 inputs, and a
                // price map that is not CY2027 is refused by the provenance rule inside.
                // A year this card holds no price map for is not computed at all — and says
                // WHY in its own terms, rather than through a mixed-year refusal that would
                // blame a price map the reader never asked for.
                const _ppb = (viewYear != null && !pricesForYear)
                  ? { perAcre: null, perAcreHigh: null, refused: "year_not_priced",
                      ppf: { factor: null, low: null, high: null, exact: false, basis: "refused",
                             note: fallYearReported(yields)
                               ? "not computed — this card holds no CY" + viewYear + " price table yet"
                               : "not computed — the AIP has not priced CY" + viewYear + " coverage for this farm yet" } }
                  : ppPerAcreForRow(pp, ay, prices, viewYear ?? farmer.cropYear, _ppUnits);
                const perAcre = _ppb.perAcre;
                const displayYield = pp.aphYield ?? ay?.approvedYield ?? null;
                // A year the AIP has not priced carries only a WORKING yield — shown as pending.
                const yieldPending = viewYear != null && pp.aphYield == null && !!ay && !(ay.liability != null || ay.acres != null);
                // A price the card's map cannot prove for this year is shown from the ladder, display only.
                const shownPrice = pricesForYear ? price : resolveRmaPrice((pp.crop || "").toUpperCase(), viewYear, "projected").price;
                const displayCov   = pp.coverageLevel ?? ay?.coverageLevel ?? null;
                const displayPlan  = ay?.plan ?? null;
                const unit = yieldUnit({type: pp.type, crop: pp.crop});
                const isCat = ay?.coverageType === "C";
                // ⚠ `refused` IS CARRIED. It was not, so the "CY#### pending" branch below could
                // never render and a refused guarantee showed as a bare dash.
                return { ...pp, ay, price: shownPrice, unit, maxAc, perAcre, perAcreHigh: _ppb.perAcreHigh, ppf: _ppb.ppf, refused: _ppb.refused, displayYield, yieldPending, displayCov, displayPlan, isCat };
              }).filter(r => silageHasApprovedAph(normalizeCropType(r.type) === "026", r.aphYield, r.ay));

              if (!tableRows.length) return null; // county left with nothing after silage filter

              return (
                <Card key={county} style={{ marginBottom:16 }}>
                  <SectionTitle>{county} County</SectionTitle>
                  {isMobile ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {tableRows.map((r,i) => (
                        <div key={i} style={{ background:CROP_BG[r.crop]||"#f8f4ec", border:`1px solid ${CROP_COLOR[r.crop]||"#6b4f00"}44`, borderRadius:8, padding:"12px 14px" }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                              <div style={{ width:10, height:10, borderRadius:"50%", background:CROP_COLOR[r.crop]||"#6b4f00" }} />
                              <span style={{ fontWeight:800, fontSize:14, color:"#1a2e1a" }}>{r.crop}{r.type ? ` · ${r.type}` : ""}{normalizePractice(r.practice) === "Organic" ? " · Organic" : ""}</span>
                              {r.displayPlan && <Pill color={CROP_COLOR[r.crop]||"#6b4f00"}>{r.displayPlan}</Pill>}
                            </div>
                            {r.perAcre == null && r.refused && (
                              <div title={(r.ppf && r.ppf.note) || "not computed"} style={{ fontSize:11, color:"#9a7a40", fontStyle:"italic", textAlign:"right" }}>CY{r.cropYear} pending</div>
                            )}
                            {r.perAcre != null && (
                              <div style={{ background:"#fff", border:`1px solid ${CROP_COLOR[r.crop]||"#6b4f00"}44`, borderRadius:6, padding:"5px 12px", textAlign:"center" }}>
                                <div style={{ fontSize:10, color:"#9a7a40", marginBottom:1 }}>PP Guarantee</div>
                                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:CROP_COLOR[r.crop]||"#6b4f00", fontSize:16 }}>{r.ppf && !r.ppf.exact ? <span title={r.ppf.note} style={{ fontSize:10, color:"#9a7a40", marginRight:3 }}>at least</span> : null}{fmtD(r.perAcre)}</span>
                                <span style={{ fontSize:10, color:"#aaa", marginLeft:2 }}>/ac</span>
                              </div>
                            )}
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"6px 12px", fontSize:12 }}>
                            <div><span style={{ color:"#9a7a40", fontSize:10, display:"block" }}>Coverage</span><span style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#6b4f00" }}>{covLevelText(r.displayCov, r.isCat ? "C" : null)}</span></div>
                            <div><span style={{ color:"#9a7a40", fontSize:10, display:"block" }}>APH Yield</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{r.yieldPending ? "Pending" : r.displayYield!=null?fmt(r.displayYield)+(" "+yieldUnit(r)):"—"}</span></div>
                            <div><span style={{ color:"#9a7a40", fontSize:10, display:"block" }}>PP Factor</span><span title={r.ppf ? r.ppf.note : undefined} style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, color: r.ppf && !r.ppf.exact ? "#9a7a40" : "#6b4f00" }}>{r.ppf ? ppFactorLabel(r.ppf) : (getPPFactor(r.crop)*100).toFixed(0)+"%"}</span></div>
                            <div><span style={{ color:"#9a7a40", fontSize:10, display:"block" }}>Max Elig. Acres</span><span style={{ fontVariantNumeric: "tabular-nums", fontWeight:700 }}>{fmt(r.maxAc,2)}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                      <thead>
                        <tr style={{ background:"#f8f4ec" }}>
                          {["Crop / Type","Plan","Coverage","APH Yield","Price","PP Factor","Max Elig. Acres","PP Guarantee / Acre"].map(h => (
                            <th key={h} style={{ padding:"8px 14px", textAlign:h==="Crop / Type"?"left":"right", fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#9a7a40", textTransform:"uppercase", borderBottom:"1px solid #e8e0d0", whiteSpace:"nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((r,i) => (
                          <tr key={i} style={{ borderBottom:"1px solid #f0ece4", background:i%2===0?"#fff":"#fdfaf4" }}>
                            <td style={{ padding:"10px 14px" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ width:10, height:10, borderRadius:"50%", background:CROP_COLOR[r.crop]||"#6b4f00", flexShrink:0 }} />
                                <span style={{ fontWeight:700, color:"#1a2e1a" }}>{r.crop}{r.type ? <span style={{ fontWeight:400, color:"#9a7a40", fontSize:11 }}> · {r.type}</span> : ""}{normalizePractice(r.practice) === "Organic" ? <span style={{ fontWeight:600, color:"#5a7a5a", fontSize:11 }}> · Organic</span> : ""}</span>
                              </div>
                            </td>
                            <td style={{ padding:"10px 14px", textAlign:"right" }}>
                              {r.displayPlan ? <Pill color={CROP_COLOR[r.crop]||"#6b4f00"}>{r.displayPlan}</Pill> : <span style={{ color:"#bbb" }}>—</span>}
                            </td>
                            <td style={{ padding:"10px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#6b4f00" }}>
                              {r.displayCov != null ? (r.displayCov*100).toFixed(0)+"%"+(r.isCat?" CAT":"") : <span style={{ color:"#bbb" }}>—</span>}
                            </td>
                            <td style={{ padding:"10px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>
                              {r.yieldPending
                                ? <span title="The AIP has not priced this crop year; its working yield is not yet an approved yield." style={{ color:"#9a7a40" }}>Pending</span>
                                : r.displayYield != null ? fmt(r.displayYield)+(" "+yieldUnit(r)) : <span style={{ color:"#bbb" }}>—</span>}
                            </td>
                            <td style={{ padding:"10px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums" }}>{r.price == null || !(r.price > 0) ? <span style={{ color:"#bbb" }}>—</span> : r.unit === "lbs" ? `${fmtD(r.price/100)}/lbs` : fmtD(r.price)}</td>
                            <td style={{ padding:"10px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#6b4f00" }}>
                              <span title={r.ppf ? r.ppf.note : undefined}>{r.ppf ? ppFactorLabel(r.ppf) : (getPPFactor(r.crop)*100).toFixed(0)+"%"}</span>
                            </td>
                            <td style={{ padding:"10px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700 }}>
                              {r.maxAc > 0 ? fmt(r.maxAc,2) : <span style={{ color:"#bbb" }}>—</span>}
                            </td>
                            <td style={{ padding:"10px 14px", textAlign:"right" }}>
                              {r.perAcre != null
                                ? <div style={{ display:"inline-block", background:CROP_BG[r.crop]||"#f8f4ec", border:`1px solid ${CROP_COLOR[r.crop]||"#6b4f00"}44`, borderRadius:6, padding:"4px 12px" }}>
                                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:CROP_COLOR[r.crop]||"#6b4f00", fontSize:14 }}>{r.ppf && !r.ppf.exact ? <span title={r.ppf.note} style={{ fontSize:10, color:"#9a7a40", marginRight:3 }}>at least</span> : null}{fmtD(r.perAcre)}</span>
                                    <span style={{ fontSize:10, color:"#aaa", marginLeft:3 }}>/ac</span>
                                  </div>
                                : r.refused
                                  ? <span title={(r.ppf && r.ppf.note) || "not computed"}
                                          style={{ color:"#9a7a40", fontSize:11, fontStyle:"italic" }}>CY{r.cropYear} pending</span>
                                  : <span style={{ color:"#bbb" }}>—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF EXPORT — opens a styled print window, farmer-facing leave-behind
// ─────────────────────────────────────────────────────────────────────────────
// Pre-fetch the Clover Ag logo as a base64 data URI so we can embed it
// directly into the popup PDF HTML. Loading via a network URL inside the
// popup window was racing the 400ms-then-print timer; the image often
// hadn't loaded by the time window.print() fired, so the logo rendered
// blank or as a broken-image icon. Embedding as data URI is instant.
// Tries the smaller /logo.png first, falls back to the high-res transparent
// one. Cache the result so we only fetch once per session.
let _cachedLogoDataUri = null;
async function loadLogoDataUri() {
  if (_cachedLogoDataUri) return _cachedLogoDataUri;
  const candidates = ["/logo.png", "/Clover_Ag_Logo1_transparent.png"];
  for (const path of candidates) {
    try {
      const res = await fetch(path);
      if (!res.ok) continue;
      const blob = await res.blob();
      const dataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      _cachedLogoDataUri = dataUri;
      return dataUri;
    } catch { /* try next */ }
  }
  return null; // fall back to no logo if all fetches fail
}

// ── LIVESTOCK POSITION (read for the farm coverage statement) ───────────────────
// The producer's DRP and LRP position and its estimates, read live from the synced
// endorsements (src/farm/livestockStatement.js). LRP type and coverage level are read
// from RMA's daily rate row matching each line, since the endorsement row stores
// only "Fed" / "Feeder" cattle.
async function loadLivestockStatementData(farmer, now = new Date()) {
  const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const live = (farmer.lrpPolicies || []).flatMap((p) => p.endorsements || []).filter((e) => String(e.endDate || "").slice(0, 10) >= todayISO);
  const sales = [...new Set(live.map((e) => String(e.salesDate || "").slice(0, 10)).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)))];
  const ends = [...new Set(live.map((e) => String(e.endDate || "").slice(0, 10)).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)))];
  const lrpRates = sales.length && ends.length
    ? await sbFetch("lrp_daily_rates", { select: "sales_eff_date,end_date,commodity,type_label,coverage_price,coverage_level,target_low_weight,target_high_weight,paf",
        filters: [`sales_eff_date=in.(${sales.join(",")})`, `end_date=in.(${ends.join(",")})`] }).catch(() => [])
    : [];
  const pos = livestockPosition(farmer, { asOf: todayISO, lrpRates });

  // Estimated payments (Kevin, 2026-11-03: "Add estimates"). Printed only in their own
  // marked box. A failed read leaves that line or quarter "not estimated"; it never
  // stops the statement, whose carrier figures don't depend on it.
  let est = null;
  try {
    // DRP: the DRP screen's own estimate, quarter by quarter (USDA announced prices,
    // RMA futures-implied prices for unsettled months, RMA's yield adjustment).
    const drpEst = [];
    if (pos.drp.quarters.length) {
      const ends = (farmer.drpPolicies || []).flatMap((p) => (p.endorsements || []).map((e) => ({ ...e, quarter: lsDrpQuarterOf(e) })));
      const want = new Set(pos.drp.quarters.map((q) => q.quarter));
      const live = ends.filter((e) => want.has(e.quarter));
      const cme = drpMergeForwardPrices(await fetchRmaForwardPrices(), await fetchDrpMonthEstimates());
      const yctx = await drpLoadYieldContext(live, (farmer.state || "NY").toUpperCase());
      for (const q of want) {
        const actuals = drpGetQuarterActualsEnhanced(q, {}, cme);
        const r = drpEstimateIndemnity(live.filter((e) => e.quarter === q), actuals, (e) => drpWithYieldAdj(e, yctx, actuals));
        drpEst.push({ quarter: q, amount: r ? r.amount : null, partial: !!(r && r.partial), asOf: (actuals && actuals.estAsOf) || (cme && cme.updated) || null });
      }
    }
    // LRP: RMA's newest futures-implied curve, CME settlements for nearer dates.
    let futures = [], rmaCurve = [];
    if (pos.lrp.lines.length) {
      const since = new Date(now.getTime() - 10 * 864e5).toISOString().slice(0, 10);
      futures = await sbFetch("futures_settles_daily", { select: "commodity,contract_month,settle_date,settle_price",
        filters: ["source=eq.usda_feeder", `settle_date=gte.${since}`] }).catch(() => []);
      const top = await sbFetch("lrp_daily_rates", { select: "sales_eff_date", filters: ["type_label=eq.Fed Cattle", `sales_eff_date=gte.${since}`], order: "sales_eff_date.desc", limit: 1 }).catch(() => []);
      const newest = top && top[0] ? String(top[0].sales_eff_date).slice(0, 10) : null;
      // One sales day, every end date RMA prices (the value doesn't depend on coverage level).
      const ref = newest ? await sbFetch("lrp_daily_rates", { select: "sales_eff_date,end_date,type_label,expected_end_value,paf",
        filters: ["type_label=in.(Steers Wt 2,Fed Cattle)", `sales_eff_date=eq.${newest}`] }).catch(() => []) : [];
      rmaCurve = (ref || []).filter((r) => Number(r.paf) > 0 && Number(r.expected_end_value) > 0)
        .map((r) => ({ kind: r.type_label === "Fed Cattle" ? "live_cattle" : "feeder_cattle", date: r.end_date, base: Number(r.expected_end_value) / Number(r.paf), asOf: newest }));
    }
    est = livestockEstimates(pos, { drp: drpEst, futures, rmaCurve });
  } catch (_) { est = null; }
  pos.est = est;
  return pos;
}

// ── FARM COVERAGE STATEMENT (lender copy) ─────────────────────────────────────
// Kevin, 2026-11-03, for a bank lender: "1.) a farms insurance position for current
// year. 2.) Any insurance payments so far this year for the particular farm 3.) Any
// government payments projected or received" — ARC/PLC as an estimated payment per
// base acre by crop and county "without printing a false number", and "This document
// will need to be ever updating based on whatever time the report is requested."
// So every figure is read here, at the click, for the year it belongs to: the crop
// year in force (and next year's once on file), PRF's calendar year, DRP/LRP in force,
// paid claims, the FSA base acres on file, FSA's county figures for the program year
// FSA pays next and the one being elected, and the SCO/ECO county figures with the
// harvest price ladder. Built by src/farm/lenderStatement.js; estimates print only in
// its marked box. Agents only: it carries ARC/PLC and SCO/ECO projections.
async function exportFarmStatement(farmer) {
  const now = new Date();
  const Y = Number(farmer.cropYear) || CURRENT_CROP_YEAR;
  const { farmerIds, policyIds } = farmPageIds(farmer);
  const st2 = String(farmer.state || "NY").toUpperCase();
  const none = () => [];
  try {
    // 1. Crop units for the crop year in force and the next one, with each policy's number and carrier.
    const [pols, units] = policyIds.length ? await Promise.all([
      sbFetch("policies", { select: "id,policy_number,aip,crop_year,policy_type,state", filters: [`id=in.(${policyIds.join(",")})`] }),
      sbFetch("crop_coverage_units", { select: "policy_id,crop_year,county,crop,plan,type_code,practice_code,unit_number,unit_structure,is_prevent_plant,acres,aph_yield,liability,total_premium,net_premium,coverage_level",
        filters: [`policy_id=in.(${policyIds.join(",")})`, `crop_year=in.(${Y},${Y + 1})`] }),
    ]) : [[], []];
    const polById = new Map((pols || []).map((p) => [p.id, p]));
    const unitRows = (units || []).map((u) => ({ ...u, policy_number: (polById.get(u.policy_id) || {}).policy_number || null, aip: (polById.get(u.policy_id) || {}).aip || null }));
    const counties = [...new Set(unitRows.map((u) => String(u.county || "").toUpperCase()).filter(Boolean))];
    const crops = [...new Set(unitRows.map((u) => String(u.crop || "").toUpperCase()).filter(Boolean))];

    // 2. PRF: this calendar year's policies and their grid indices.
    const prfYear = prfCoverageYear(now);
    const prfPolicies = prfCoverageState(farmer.prfPolicies || [], prfYear).policies;
    const grids = [...new Set(prfPolicies.flatMap((p) => (p.intervals || []).map((iv) => iv.gridId || iv.grid_id)).filter(Boolean).map(String))];
    const idxRows = grids.length ? await sbFetch("prf_grid_indices", { select: "grid_id,interval_code,final_index_value,is_final,estimated_index_value,estimated_rainfall_inches,normal_rainfall_inches,normal_to_date_inches,estimated_as_of",
      filters: [`grid_id=in.(${grids.join(",")})`, `crop_year=eq.${prfYear}`] }).catch(none) : [];
    const index = {};
    for (const r of idxRows || []) index[`${r.grid_id}|${r.interval_code}`] = { finalValue: r.is_final ? r.final_index_value : null, estimatedValue: r.estimated_index_value,
      estimatedInches: r.estimated_rainfall_inches, normalInches: r.normal_rainfall_inches, normalToDateInches: r.normal_to_date_inches, estimatedAsOf: r.estimated_as_of };

    // 3. DRP and LRP: the livestock statement's own read.
    const hasLivestock = (farmer.drpPolicies || []).length > 0 || (farmer.lrpPolicies || []).length > 0;
    const livePos = hasLivestock ? await loadLivestockStatementData(farmer, now) : null;

    // 4. FSA base acres: a signed CCC-866 (with the producer's share) wins, else the FSA-156EZ.
    const fIn = farmerIds.length ? `farmer_id=in.(${farmerIds.join(",")})` : null;
    let m866 = null, m156 = null;
    if (fIn) {
      const ups866 = await sbFetch("ccc866_uploads", { filters: [fIn], order: "program_year.desc,farmer_id.asc,state_code.asc,county_code.asc" }).catch(none);
      const y866 = ups866 && ups866[0] ? ups866[0].program_year : null;
      if (y866 != null) {
        const f = [fIn, `program_year=eq.${y866}`];
        const [elections, shares] = await Promise.all([sbFetch("ccc866_farm_elections", { filters: f }).catch(none), sbFetch("ccc866_producer_shares", { filters: f }).catch(none)]);
        m866 = CCC866.electionModel({ upload: CCC866.mergeUploads866(ups866.filter((u) => u.program_year === y866)), elections: elections || [], shares: shares || [] },
          { keys: farmerNameKeys(farmer), norm: normFarmerName });
      }
      const ups156 = await sbFetch("fsa_156ez_uploads", { select: "crop_year,prepared_date", filters: [fIn], order: "crop_year.desc,farmer_id.asc,admin_state.asc,admin_county.asc" }).catch(none);
      const y156 = ups156 && ups156[0] ? ups156[0].crop_year : null;
      if (y156 != null) {
        const f = [fIn, `crop_year=eq.${y156}`];
        const [farms, base] = await Promise.all([sbFetch("fsa_farm_records", { filters: f }).catch(none), sbFetch("fsa_base_acres", { filters: f }).catch(none)]);
        m156 = FSA156.baseAcreModel({ upload: { crop_year: y156 }, farms: farms || [], tracts: [], base: base || [] });
      }
    }
    const base = lenderBaseFromModels({ m866, m156 });

    // 5. County figures: FSA's for the program year FSA pays next (Y−1) and the one being
    // elected (Y); RMA's SCO/ECO county rows for the crop years shown; the Silas county
    // yield model for NY corn; the harvest price ladder.
    const progYears = [Y - 1, Y];
    const progCounties = base ? [...new Set(base.rows.map((r) => String(r.county || "").toUpperCase()))] : counties;
    const [arcRows, refRows, scoRows, miRows] = await Promise.all([
      progCounties.length ? sbFetch("arc_co_county", { select: "program_year,crop,state,county,practice,benchmark_yield,benchmark_revenue,guarantee_revenue,actual_yield,actual_revenue,payment_factor,payment_rate",
        filters: [`program_year=in.(${progYears.join(",")})`, `state=eq.${st2}`], order: "id.asc" }).catch(none) : [],
      sbFetch("arc_plc_reference", { filters: [`program_year=in.(${progYears.join(",")})`] }).catch(none),
      counties.length ? sbFetch("sco_eco_county", { select: "crop_year,crop,state,county,expected_yield,rma_final_yield,projected_price,harvest_price,expected_revenue",
        filters: [`crop_year=in.(${Y - 1},${Y},${Y + 1})`, `state=eq.${st2}`] }).catch(none) : [],
      sbFetch("mi_model_snapshot", { select: "crop_year,payload", filters: [`crop_year=in.(${progYears.join(",")})`] }).catch(none),
    ]);
    const arc = {}; for (const r of arcRows || []) (arc[r.program_year] = arc[r.program_year] || []).push(r);
    const ref = {}; for (const r of refRows || []) (ref[r.program_year] = ref[r.program_year] || {})[String(r.crop).toUpperCase()] = r;
    const miBy = {}; for (const r of miRows || []) miBy[r.crop_year] = (r.payload && r.payload.mi_counties) || [];
    const yf = {}; for (const yr of progYears) yf[yr] = st2 === "NY" ? silasCornFactors(miBy[yr], scoRows, { cropYear: yr, state: st2 }).factors : {};
    const harvest = {};
    const discSyms = [...new Set(crops.map((c) => discoveryContractSym(c, Y)).filter(Boolean))];
    const barRows = discSyms.length ? await sbFetch("futures_contract_history", { select: "contract_sym,bar_date,close", filters: [`contract_sym=in.(${discSyms.join(",")})`], order: "bar_date.asc" }).catch(none) : [];
    const bars = {}; for (const r of barRows || []) (bars[r.contract_sym] = bars[r.contract_sym] || []).push(r);
    for (const c of crops) {
      const res = resolveRmaPrice(c, Y, "harvest", bars[discoveryContractSym(c, Y)] || null);
      if (!res || res.price == null || res.conflict) continue;
      const px = "$" + Number(res.price).toFixed(2);
      if (res.status === "final") harvest[c] = { price: res.price, final: true, label: `${px}, RMA final` };
      else if (res.status === "estimate") harvest[c] = { price: res.price, final: false,
        label: `${px}, discovery-month average (${res.days} of ${res.expectedDays} days${res.closed ? "; RMA not yet announced" : "; still moving"})` };
    }

    // What each payment was for: the stage codes on the carrier's P21 worksheet lines
    // (where the money went), keyed exactly as the Claims page keys them (policy, claim
    // number, crop year). Missing lines leave the claim's notice stages to label it.
    const claimPolIds = [...new Set((farmer.claims || []).filter((c) => c.status === "Paid" && (c.policyType || "crop") === "crop").map((c) => c.policyId).filter(Boolean))];
    const p21Rows = claimPolIds.length ? await sbFetch("indemnity_lines", { select: "policy_id,loss_key_base,crop_year,stage_code",
      filters: [`policy_id=in.(${claimPolIds.join(",")})`, "source=eq.p21", `crop_year=in.(${Y - 1},${Y})`] }).catch(none) : [];
    const p21By = new Map();
    for (const l of p21Rows || []) {
      if (!l.stage_code) continue;
      const k = `${l.policy_id}|${String(l.loss_key_base || "").trim()}|${l.crop_year}`;
      if (!p21By.has(k)) p21By.set(k, new Set());
      p21By.get(k).add(String(l.stage_code).trim().toUpperCase());
    }
    const claimsTyped = (farmer.claims || []).map((c) => ({ ...c,
      paidStages: [...(p21By.get(`${c.policyId}|${String(c.claimNumber || "").trim()}|${c.cropYear}`) || [])] }));

    const st = lenderStatement({ asOf: now.toISOString(), farmerName: farmer.name || "", cropYear: Y, units: unitRows,
      prf: prfPolicies.length ? { year: prfYear, policies: prfPolicies, index } : null,
      livestock: livePos ? { pos: livePos, est: livePos.est || null } : null,
      claims: claimsTyped,
      scoEco: { state: st2, county: scoRows || [], harvest, yieldFactor: yf[Y] || {} },
      programs: { base, counties: progCounties, crops, arc, ref, yieldFactor: yf, programYears: progYears } });
    const logoDataUri = await loadLogoDataUri();
    const html = lenderStatementHTML(st, { agentName: farmer._agentOverride || farmer.agent || "",
      logoSrc: logoDataUri || `${window.location.origin}/Clover_Ag_Logo1_transparent.png` });
    const url = URL.createObjectURL(new Blob([withPrintBackBar(html)], { type: "text/html;charset=utf-8" }));
    const win = window.open(url, "_blank", "width=1100,height=800");
    if (!win) { alert("Popup blocked — please allow popups for this site and try again."); URL.revokeObjectURL(url); return; }
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
  } catch (err) {
    alert("Failed to build the farm statement: " + (err && err.message ? err.message : "unknown error"));
  }
}


async function generatePolicyPDF(farmer, section, prices, priceMapForYear = null) {
  // CLAIM ESTIMATES ARE ADMIN-ONLY ON THIS SHEET (2026-10-21). The Export PDF button is
  // on FarmerView, which the portal renders, so a grower can print this. The EFFECTIVE
  // identity decides — false in the portal and in preview-as — exactly as the DRP card
  // does on screen. Guarded by tools/check-estimate-print.js.
  const showEstPrint = isCurrentUserAdmin();
  // HOISTED to the whole PDF (2026-09-04). It used to be declared inside the
  // spring/fall branch, so the PREVENT PLANT section — a different branch of the
  // same function — could not see it. That did not throw while nothing read it
  // there; the moment the PP factor resolver was wired to RMA's own stated
  // per-unit guarantee it became a ReferenceError, and check-undef named the
  // line. One map for the document, so the PP split and the PP factor on the
  // same printed page cannot resolve from different sources.
  let _unitsByKey = null;
  if (section === "spring" || section === "fall" || section === "pp") {
    _unitsByKey = await fetchCoverageUnitsByKey(farmer).catch(() => null);
  }
  const d  = (n, dec=2) => n == null ? "—" : "$" + Number(n).toLocaleString("en-US", {minimumFractionDigits:dec, maximumFractionDigits:dec});
  const d0 = (n)        => n == null ? "—" : "$" + Number(n).toLocaleString("en-US", {minimumFractionDigits:0,  maximumFractionDigits:0});
  const f  = (n, dec=0) => n == null ? "—" : Number(n).toLocaleString("en-US", {minimumFractionDigits:dec, maximumFractionDigits:dec});
  const pct = (n)       => n == null ? "—" : (Number(n)*100).toFixed(0) + "%";

  const generated = new Date().toLocaleDateString("en-US", {month:"long", day:"numeric", year:"numeric"});

  // ── SHARED CSS ──────────────────────────────────────────────────────────────
  // Sized for phone-screenshot readability (the popup window is the user's
  // primary delivery vector — they screenshot it and text the image to the
  // farmer). Print PDF still works fine since @page rules + page-break
  // hints are intact. Body is centered at max-width 1050px so the popup at
  // 900px and the full landscape page (~10" wide) both render at the same
  // visual scale instead of expanding tables to whatever the viewport is.
  let css = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Lora', serif; font-size: 12pt; color: #1a2e1a; background: #fff; max-width: 1050px; margin: 0 auto; }
    @page { size: letter landscape; margin: 0.4in 0.4in; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; max-width: none; }
      .no-print { display: none !important; }
      .page-break { page-break-before: always; }
      .pp-county { page-break-inside: avoid; }
      .county-block { page-break-inside: avoid; }
      table tr { page-break-inside: avoid; }
      .pp-edu-cols p { page-break-inside: avoid; }
    }
    table { font-size: 10.5pt; }
    th { padding: 8px 10px !important; }
    td { padding: 8px 10px !important; }
    .header { background: #1a2e1a; color: #fff; padding: 16px 28px 14px; margin-bottom: 0; display: flex; align-items: stretch; gap: 20px; }
    .header-text { flex: 1 1 auto; min-width: 0; }
    .header-logo { flex: 0 0 auto; display: flex; align-items: center; }
    .header-logo img { height: 64px; width: auto; display: block; }
    .header-sub { font-size: 9pt; font-weight: 700; letter-spacing: 0.14em; color: #4a9a4a; text-transform: uppercase; margin-bottom: 4px; }
    .header-name { font-size: 24pt; font-weight: 800; color: #fff; margin-bottom: 4px; line-height: 1.1; }
    .header-meta { font-size: 10pt; color: #8aca8a; display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
    .stat-row { display: flex; gap: 28px; flex-wrap: wrap; }
    .stat { }
    .stat-val { font-size: 16pt; font-weight: 800; color: #fff; line-height: 1; }
    .stat-lbl { font-size: 7.5pt; font-weight: 800; letter-spacing: 0.1em; color: #4a7a4a; text-transform: uppercase; margin-top: 3px; }
    .body { padding: 16px 28px; }
    .section-title { font-size: 13pt; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #4a7a4a; margin: 12px 0 6px; padding-bottom: 4px; border-bottom: 1.5px solid #d0e4d0; }
    table { width: 100%; border-collapse: collapse; font-size: 11pt; margin-bottom: 10px; font-family: 'Plus Jakarta Sans', Arial, sans-serif; }
    th { background: #f0f6f0; padding: 7px 12px; text-align: right; font-size: 9pt; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #4a7a4a; border-bottom: 1.5px solid #c8d8c8; white-space: nowrap; }
    th:first-child { text-align: left; }
    td { padding: 8px 12px; text-align: right; border-bottom: 1px solid #edf4ed; vertical-align: top; }
    td:first-child { text-align: left; font-weight: 700; }
    tr:last-child td { border-bottom: none; }
    .total-row td { font-weight: 800; background: #f4f8f4; border-top: 2px solid #c8d8c8; }
    .highlight { color: #2d6a2d; font-weight: 800; }
    .muted { color: #aaa; }
    .pill { display: inline-block; padding: 3px 9px; border-radius: 3px; font-size: 9pt; font-weight: 800; background: #e8f4e8; color: #2d6a2d; border: 1px solid #b0d4b0; }
    .footer { padding: 10px 28px; border-top: 1px solid #e0e8e0; font-size: 9pt; color: #677167; display: flex; justify-content: space-between; align-items: center; gap: 16px; }
    .footer-logo { height: 22px; width: auto; opacity: 0.8; }
    .county-block { margin-bottom: 12px; }
    .county-header { font-size: 12pt; font-weight: 800; color: #1a2e1a; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; }
    .county-dot { width: 9px; height: 9px; border-radius: 50%; background: #4a9a4a; display: inline-block; }
    .note-text { font-size: 9pt; color: #9a7a3a; font-style: italic; margin-top: 2px; }
    /* PP table is sparse (3 columns), so cap its width and center it.
       Bigger column padding lets the values breathe even with the bigger
       font; the phone screenshot is now easily readable without zooming. */
    .pp-county { margin-bottom: 12px; }
    .pp-county .county-header { font-size: 11.5pt; margin-bottom: 5px; }
    .pp-county table { margin: 0 auto 0; max-width: 760px; font-size: 12pt; }
    .pp-county th { padding: 7px 14px; font-size: 9pt; }
    .pp-county td { padding: 9px 14px; }
    /* On-screen popup view: a tighter "Back" pill in the corner so it doesn't
       dominate phone screenshots — but it's no-print so it never appears in
       the printed PDF. */
    .no-print { padding: 6px 14px !important; margin-bottom: 4px !important; }
    .no-print button { padding: 6px 14px !important; font-size: 11px !important; }
  `;

  // Embed the Clover Ag logo as a data URI so it's already in the popup
  // HTML — no network roundtrip, no race against the print() timer. Falls
  // back to an absolute URL if the fetch fails (logo just won't render).
  const logoDataUri = await loadLogoDataUri();
  const logoSrc = logoDataUri || `${window.location.origin}/Clover_Ag_Logo1_transparent.png`;

  // ⚠ A PRINTED FALL SECTION IS ONE CROP YEAR — the year its price map proves. It used
  // to print the card's collapsed rows: CY2026 lines, CY2027 $0 SCO/ECO placeholders
  // and CY2027 prevent-plant rows beside CY2026 yields, all priced with the CY2026 map,
  // and on a farm like Thomas Nagle a CY2027 APH at the CY2026 price. The PDF is what
  // the grower keeps, so it prints exactly one year and says which. A farmer object with
  // no per-year rows (a demo or seed farm) keeps the old behaviour.
  const _fallByYear = section === "fall" && !!farmer.approvedYieldsByYear;
  const _pdfMapFor = (y) => (priceMapForYear ? priceMapForYear(y) : prices);
  const _fallYears = _fallByYear ? fallYearsOnFile(farmer) : [];
  // The newest fall year whose price map is PROVEN to be that year's. A year no map can
  // prove is never printed with a borrowed price — it is named instead.
  const _fallRowsFor = (y) => ((farmer.approvedYieldsByYear || {})[y] || []).filter(a => FALL_CROPS.includes((a.crop || "").toUpperCase()));
  // The same test the card applies before it prints a priced table: the map is that year's,
  // the AIP has priced the year, and the map can price every line of it.
  const _fallPdfYear = _fallByYear
    ? (_fallYears.find(y => { const m = _pdfMapFor(y); return !!m && m._cropYear === y
        && fallYearReported(_fallRowsFor(y)) && fallYearPriceable(_fallRowsFor(y), m); }) ?? null) : null;
  // …and the units of THAT year, not the clicked card's.
  if (_fallByYear && _fallPdfYear != null && _fallPdfYear !== (Number(farmer.cropYear) || CURRENT_CROP_YEAR)) {
    _unitsByKey = await fetchCoverageUnitsByKey(farmer, _fallPdfYear).catch(() => null);
  }
  const _fallNewerUnpriced = _fallByYear ? _fallYears.filter(y => _fallPdfYear == null || y > _fallPdfYear) : [];
  const _fallYearNotice = (y) => fallYearReported(_fallRowsFor(y))
    ? "CY" + y + " is not printed here — this page cannot yet price it"
    : "CY" + y + ": the AIP has not priced this policy yet, so no CY" + y + " guarantee is shown";

  // ── HEADER HTML (shared) ────────────────────────────────────────────────────
  const sectionLabels = { spring:"Spring Crops", fall:"Fall Crops", drp:"Dairy Revenue Protection", lrp:"Livestock Risk Protection", prf:"Pasture, Rangeland & Forage", pp:"Prevented Planting Summary" };
  const headerHTML = `
    <div class="header">
      <div class="header-text">
        <div class="header-sub">Clover Ag Risk Management · Spartan Insurance Agency · ${sectionLabels[section]||section}</div>
        <div class="header-name">${farmer.name || "—"}</div>
        <div class="header-meta">
          <span>${farmer.policyNumber || "—"}</span>
          ${farmer.insurer ? `<span>·</span><span>${farmer.insurer}</span>` : ""}
          <span>·</span><span>Crop Year: ${_fallByYear ? (_fallPdfYear ?? "—") : (farmer.cropYear || "—")}</span>
          ${farmer.agent ? `<span>·</span><span>Agent: ${isCurrentUserAdmin() ? farmer.agent : "Clover Ag"}</span>` : ""}
          ${farmer.address ? `<span>·</span><span>${farmer.address}</span>` : ""}
        </div>
  `;

  const footerHTML = `
    <div class="footer">
      <span><strong style="color:#2d5a10">Clover Ag Risk Management</strong> · Spartan Insurance Agency · cloverag.com · (585) 233-1066</span>
      <span>Generated ${generated} · For informational purposes only. Subject to policy terms and conditions.</span>
    </div>
  `;

  // ── SECTION-SPECIFIC BODY ───────────────────────────────────────────────────
  let statRowHTML = "";
  let bodyHTML = "";

  // ── SPRING / FALL ──
  if (section === "spring" || section === "fall") {
    
    const cropYear = _fallByYear ? (_fallPdfYear ?? farmer.cropYear) : (farmer.cropYear || 2026);
    // BUG-096 — the printed fall section prints the card's approved yield, resolved per APH
    // database by the same rule; a read that fails prints no approved yield, never the summary.
    const _fallAph = _fallByYear && _fallPdfYear != null
      ? await fetchFallWheatUnits(farmer).then((u) => ({ units: u, status: "ready" })).catch(() => ({ units: null, status: "error" }))
      : null;
    const _fallResolve = (rows, y) => _fallAph ? resolveFallRows(rows, { year: y, ..._fallAph }) : rows;
    const allYields = _fallByYear ? (_fallPdfYear != null ? _fallResolve(((farmer.approvedYieldsByYear || {})[_fallPdfYear] || []), _fallPdfYear) : []) : (farmer.approvedYields || []);
    const yields = section === "spring"
      ? allYields.filter(a => !FALL_CROPS.includes((a.crop||"").toUpperCase()))
      : allYields.filter(a =>  FALL_CROPS.includes((a.crop||"").toUpperCase()));

    const counties = [...new Set(yields.map(r => r.county))].filter(Boolean);

    // Prevent-plant acres per (crop, county). generatePolicyPDF is a MODULE-LEVEL
    // function, so the components' ppFor helper is NOT in scope here — referencing
    // it threw at print time. crop_coverage.pp_acres is the authoritative source
    // (it superseded the old note that planted acres "come from PP tab inputs
    // which aren't persisted"), so fetch it rather than print insured acres.
    const _pids = (farmer.allPolicyIds || farmer._allPolicyIds || []);
    let _ppByKey = {}, _uiByKey = {};
    if (_pids.length) {
      try {
        const _ppRows = await sbFetch("crop_coverage", {
          select: PP_UI_SELECT,
          filters: [`policy_id=in.(${_pids.join(",")})`, PP_UI_FILTER] });
        ({ pp: _ppByKey, ui: _uiByKey } = buildPpUiMaps(_ppRows));
      } catch (e) { /* no PP data → prints insured acres, same as before */ }
    }
    // Takes the ROW, exactly as the screen's ppFor does — the PDF is the document
    // the grower reads, so it must resolve the split identically.
    const ppFor = (row) => _ppByKey[ppKeyOfRow(row)] || 0;
    const uiFor = (row) => _uiByKey[ppKeyOfRow(row)] || 0;

    // The same unit map the screen uses, so plantedSplit() resolves identically
    // here. Without it the PDF would model the PP split while the screen sums it —
    // and the PDF is the document the grower actually reads.

    const pdfProcBeansPrice = parseFloat(farmer.metadata?.processingBeansPrice) || 0;
    // The printed fall section is priced with ITS year's map, never the clicked card's.
    const _secPrices = _fallByYear && _fallPdfYear != null ? _pdfMapFor(_fallPdfYear) : prices;
    const pdfPrices = pdfProcBeansPrice > 0 ? repriceMap(_secPrices, { "PROCESSING BEANS": pdfProcBeansPrice }) : _secPrices;
    let totalGuarantee = 0;
    yields.forEach(r => {
      const { price } = getCropPriceAndUnit(r, pdfPrices, cropYear);
      const { price: _p, unit: _u } = getCropPriceAndUnit(r, pdfPrices, cropYear);
      const gpa = (r.approvedYield||0) * price * (r.coverageLevel||0) * (_u === "lbs" ? 0.01 : 1);
      if (r.acres) totalGuarantee += gpa * r.acres;
    });

    const ppElig = (_fallByYear ? (_fallPdfYear != null ? ((farmer.ppEligibilityByYear || {})[_fallPdfYear] || []) : []) : (farmer.ppEligibility||[])).filter(p =>
      section === "fall"
        ? FALL_CROPS.includes((p.crop||"").toUpperCase())
        : !FALL_CROPS.includes((p.crop||"").toUpperCase())
    );

    // Banner stats from M13 data (liability/premium) — more accurate than calculated guarantee
    const pdfScoEco = (farmer.scoEcoEndorsements||[]).filter(e => (!_fallByYear || (_fallPdfYear != null && Number(e.cropYear) === _fallPdfYear)) &&
      section === "fall"
        ? ["WHEAT","BARLEY","TRITICALE","RYE","WINTER WHEAT"].includes((e.crop||"").toUpperCase())
        : !["WHEAT","BARLEY","TRITICALE","RYE","WINTER WHEAT"].includes((e.crop||"").toUpperCase())
    );
    const allPdfRows = [...yields, ...pdfScoEco];
    const totalLiabilityPDF  = allPdfRows.reduce((s,r) => s + (r.liability ? Number(r.liability) : 0), 0);
    const totalFarmerPremPDF = allPdfRows.reduce((s,r) => {
      if (r.netPremium != null) return s + Number(r.netPremium);
      if (r.totalPremium != null && r.subsidy != null) return s + Number(r.totalPremium) - Number(r.subsidy);
      return s + (r.totalPremium ? Number(r.totalPremium) : 0);
    }, 0);
    const totalAcresPDF = yields.reduce((s,r) => s + (r.acres ? Number(r.acres) : 0), 0);

    statRowHTML = `
      <div class="stat-row">
        <div class="stat"><div class="stat-val">${counties.length}</div><div class="stat-lbl">Counties</div></div>
        <div class="stat"><div class="stat-val">${yields.length}</div><div class="stat-lbl">Crop Rows</div></div>
        ${totalLiabilityPDF > 0 ? `<div class="stat"><div class="stat-val">${d0(totalLiabilityPDF)}</div><div class="stat-lbl">Total Liability</div></div>` : totalGuarantee > 0 ? `<div class="stat"><div class="stat-val">${d0(totalGuarantee)}</div><div class="stat-lbl">Est. Total Guarantee</div></div>` : ""}
        ${totalAcresPDF > 0 ? `<div class="stat"><div class="stat-val">${f(totalAcresPDF,2)} ac</div><div class="stat-lbl">Insured Acres</div></div>` : ""}
        ${totalFarmerPremPDF > 0 ? `<div class="stat"><div class="stat-val">${d0(totalFarmerPremPDF)}</div><div class="stat-lbl">Farmer Premium</div></div>` : ""}
        ${ppElig.length > 0 ? `<div class="stat"><div class="stat-val">${ppElig.length}</div><div class="stat-lbl">PP Eligible Crops</div></div>` : ""}
      </div>`;

    // ── REVENUE GUARANTEE ──
    // SCO/ECO for this section
    
    const scoEcoPDF = (farmer.scoEcoEndorsements||[]).filter(e => (!_fallByYear || (_fallPdfYear != null && Number(e.cropYear) === _fallPdfYear)) &&
      section === "fall"
        ? FALL_CROPS.includes((e.crop||"").toUpperCase())
        : !FALL_CROPS.includes((e.crop||"").toUpperCase())
    );
    // Include counties from SCO/ECO too
    const allCountiesPDF = [...new Set([...yields.map(r => r.county), ...scoEcoPDF.map(e => e.county)])].filter(Boolean);

    // A newer fall crop year the AIP has not priced is NAMED, not silently left off.
    if (_fallNewerUnpriced.length) {
      bodyHTML += `<p style="margin:6px 0 14px;font-size:10pt;color:#6b4f00">${_fallPdfYear != null ? "This page is the CY" + _fallPdfYear + " policy as reported. " : ""}${_fallNewerUnpriced.map(_fallYearNotice).join(". ")}.</p>`;
    }

    // ── PREVENT PLANT ──
    if (ppElig.length > 0) {
      bodyHTML += `<div class="section-title">Prevent Plant Eligibility</div>`;
      const ppCounties = [...new Set(ppElig.map(p => p.county))].filter(Boolean);
      ppCounties.forEach(county => {
        const cRows = ppElig.filter(p => p.county === county);
        const tableRows = cRows.map(pp => {
          const _pCUp=(pp.county||"").toUpperCase().trim(),_pCrUp=(pp.crop||"").toUpperCase(),_pTUp=(pp.type||"").toUpperCase();
          const _yBase=yields.filter(a=>(a.county||"").toUpperCase().trim()===_pCUp&&(a.crop||"").toUpperCase()===_pCrUp&&(!_pTUp||(a.type||"").toUpperCase()===_pTUp));
          const ay=_yBase.find(a=>normalizePractice(a.practice).toUpperCase()===normalizePractice(pp.practice).toUpperCase())||_yBase[0]||null;
          const _ppb = ppPerAcreForRow(pp, ay, prices, cropYear,
            ay && _unitsByKey ? _unitsByKey[cuKey(ay.county, ay.crop, ay.plan, ay.typeCode, ay.practiceCode)] : null);
          const perAcre = _ppb.perAcre;
          const maxAc   = maxAcres(pp);
          return { ...pp, ay, perAcre, ppf: _ppb.ppf, maxAc };
        }).filter(r => r.maxAc > 0 || r.perAcre != null);

        if (tableRows.length === 0) return;

        bodyHTML += `<div class="county-block pp-county">
          <div class="county-header"><span class="county-dot"></span>${county} County</div>
          <table>
            <thead><tr>
              <th style="text-align:left">Crop / Type</th>
              <th style="text-align:right">Max Elig. Acres</th>
              <th style="text-align:right">PP Guarantee / Acre</th>
            </tr></thead>
            <tbody>`;
        tableRows.forEach(r => {
          bodyHTML += `<tr>
            <td style="font-weight:700">${r.crop||"—"}${r.type ? ` · ${r.type}` : ""}${normalizePractice(r.practice) === "Organic" ? " · Organic" : ""}</td>
            <td style="text-align:right; font-family:monospace; font-weight:800">${r.maxAc > 0 ? f(r.maxAc,1) : '<span class="muted">—</span>'}</td>
            <td style="text-align:right" class="highlight">${r.perAcre != null ? `<span style="font-weight:800">${r.ppf && !r.ppf.exact ? "at least " : ""}${d(r.perAcre)}/ac</span>` : '<span class="muted">—</span>'}</td>
          </tr>`;
        });
        bodyHTML += `</tbody></table></div>`;
      });
    }
    bodyHTML += `<div class="page-break"></div><div class="section-title">Revenue Guarantee</div><div style="font-size:9pt; color:#60735f; margin:-8px 0 14px; font-style:italic">Guarantee per acre based on approved APH yield · Liability and premium figures from policy records</div>`;
    allCountiesPDF.forEach(county => {
      const rpRows = yields.filter(r => r.county === county);
      const scoRows = scoEcoPDF.filter(s => s.county === county && ((s.plan||"").toUpperCase().includes("SCO") || (s.plan||"").toUpperCase() === "ARPI"));
      const ecoRows = scoEcoPDF.filter(s => s.county === county && (s.plan||"").toUpperCase().includes("ECO"));

      // County totals across base + SCO + ECO
      const allRows = [...rpRows, ...scoRows, ...ecoRows];
      // Totals must add up the same rows the table prints — planted acres and the
      // planted portion of liability — or the county line contradicts the detail
      // above it. Both on-screen totals already do this.
      const totAcres  = rpRows.reduce((s,r) =>
        s + (r.acres != null ? (plantedSplit(r, ppFor(r), _unitsByKey).acres || 0) : 0), 0);
      const totLiab   = allRows.reduce((s,r) => {
        if (r.liability == null) return s;
        return s + (plantedSplit(r, ppFor(r), _unitsByKey).liability || 0);
      }, 0);
      const totGross  = allRows.reduce((s,r) => s + (r.totalPremium ? Number(r.totalPremium) : 0), 0);
      const totFarmer = allRows.reduce((s,r) => {
        if (r.netPremium != null) return s + Number(r.netPremium);
        if (r.totalPremium != null && r.subsidy != null) return s + Number(r.totalPremium) - Number(r.subsidy);
        return s + (r.totalPremium ? Number(r.totalPremium) : 0);
      }, 0);

      bodyHTML += `<div class="county-block">
        <div class="county-header">
          <span class="county-dot"></span>${county} County
        </div>
        <table>
          <thead><tr>
            <th>Crop</th><th>Plan</th><th>Coverage</th><th>Appr. Yield</th><th>Price</th><th>Guar./Acre</th><th>Acres</th><th>Liability</th><th>Gross Premium</th><th>Farmer Premium</th>
          </tr></thead>
          <tbody>`;
      rpRows.forEach(r => {
        const { price, unit } = getCropPriceAndUnit(r, pdfPrices, cropYear);
        // Split PP out exactly as the on-screen table does. Without this the printed
        // sheet showed INSURED acres and the blended liability while the screen showed
        // PLANTED acres and the planted portion — the same policy reading two different
        // ways depending on whether you looked at it or printed it.
        const _pv       = plantedView(r, ppFor(r),
                            (r.approvedYield||0) * price * (r.coverageLevel||0) * (unit === "lbs" ? 0.01 : 1),
                            _unitsByKey ? _unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode, r.practiceCode)] : null);
        const acres     = _pv.acres;
        const liability = _pv.liability;
        const gpa       = _pv.gpa;
        const grossPrem = r.totalPremium != null ? Number(r.totalPremium) : null;
        const farmerPrem = r.netPremium != null ? Number(r.netPremium)
          : (grossPrem != null && r.subsidy != null) ? grossPrem - Number(r.subsidy)
          : grossPrem;
        const unitNote  = r.unitCount > 1 ? `<div class="note-text">avg of ${r.unitCount} sections</div>` : "";
        const _supp = matchSupplementals(scoRows, ecoRows, r, rpRows);
        const matchSCO = _supp.sco, matchECO = _supp.eco;
        bodyHTML += `<tr>
          <td>${r.crop}${r.type ? ` · ${r.type}` : ""}</td>
          <td style="text-align:center"><span class="pill">${r.plan||"RP"}</span></td>
          <td>${covLevelText(r.coverageLevel, r.coverageType)}</td>
          <td>${f(r.approvedYield)} ${unit}${unitNote}</td>
          <td>${unit === "lbs" ? `${d(price/100)}/lbs` : `${d(price)}/${unit}`}</td>
          <td class="highlight">${d(gpa)}/ac</td>
          <td>${acres != null ? f(acres,2) : '<span class="muted">—</span>'}${
            uiFor(r) > 0
              ? `<div class="note-text">+${f(uiFor(r),1)} uninsurable</div>` : ""}${
            // The printed sheet is what the grower reads, and it showed planted
            // acres beside a premium billed on planted + PP with nothing saying so.
            ppFor(r) > 0
              ? `<div class="note-text">planted · +${f(ppFor(r),1)} PP billed</div>` : ""}</td>
          <td>${liability != null ? d0(liability) : '<span class="muted">—</span>'}</td>
          <td>${grossPrem != null ? d0(grossPrem) : '<span class="muted">—</span>'}</td>
          <td class="highlight">${farmerPrem != null ? d0(farmerPrem) : '<span class="muted">—</span>'}</td>
        </tr>`;
        // SCO/ECO sub-rows
        [...matchSCO.map(s=>({...s,_tag:"SCO"})), ...matchECO.map(s=>({...s,_tag:"ECO"}))].forEach(s => {
          const isSCO = s._tag === "SCO";
          const baseCov  = r.coverageLevel ? (r.coverageLevel*100).toFixed(0) : "75";
          const ecoCov   = s.coverageLevel ? (s.coverageLevel*100).toFixed(0) : "95";
          const bandLow  = isSCO ? baseCov : "86";
          const bandHigh = isSCO ? "86" : ecoCov;
          const sLiab    = s.liability != null ? Number(s.liability) : null;
          const sGross   = s.totalPremium != null ? Number(s.totalPremium) : null;
          const sFarmer  = s.netPremium != null ? Number(s.netPremium)
            : (sGross != null && s.subsidy != null) ? sGross - Number(s.subsidy) : sGross;
          const sAcres   = s.acres != null && Number(s.acres) > 0 ? Number(s.acres) : null;
          const guarPerAcre = (sLiab != null && sAcres != null) ? sLiab / sAcres : null;
          const planLabel = s.plan === "ARPI" ? "SCO-RP" : s.plan;
          const color = isSCO ? "#7a5a1a" : "#4a6a8a";
          bodyHTML += `<tr style="background:${isSCO ? "#fdf8ee" : "#f0f5fa"}; font-size:9pt">
            <td style="padding-left:24px; color:${color}">└ supplemental</td>
            <td style="text-align:center"><span class="pill" style="background:${color}; color:#fff">${planLabel}</span></td>
            <td style="color:${color}; font-weight:700">${bandLow}–${bandHigh}%</td>
            <td class="muted">—</td>
            <td class="muted">—</td>
            <td>${guarPerAcre != null ? `${d(guarPerAcre)}/ac` : '<span class="muted">—</span>'}</td>
            <td>${sAcres != null ? f(sAcres,2) : '<span class="muted">—</span>'}</td>
            <td style="color:${color}">${sLiab != null ? d0(sLiab) : '<span class="muted">—</span>'}</td>
            <td style="color:${color}">${sGross != null ? d0(sGross) : '<span class="muted">—</span>'}</td>
            <td style="color:${color}; font-weight:800">${sFarmer != null ? d0(sFarmer) : '<span class="muted">—</span>'}</td>
          </tr>`;
        });
      });
      // County total row
      if (totLiab > 0 || totGross > 0) {
        bodyHTML += `<tr class="total-row">
          <td colspan="6" style="font-weight:800; text-transform:uppercase; font-size:8pt; letter-spacing:0.06em">County Total</td>
          <td>${totAcres > 0 ? f(totAcres,2) : "—"}</td>
          <td>${totLiab > 0 ? d0(totLiab) : "—"}</td>
          <td>${totGross > 0 ? d0(totGross) : "—"}</td>
          <td class="highlight">${totFarmer > 0 ? d0(totFarmer) : "—"}</td>
        </tr>`;
      }
      bodyHTML += `</tbody></table></div>`;
    });

    // THE PRINTED SHEET IS WHAT THE GROWER ACTUALLY HOLDS, so it carries the
    // same reconciliation as the screen — from the SAME rule, never a second
    // copy of it. This page has been wrong exactly this way before: it once
    // printed insured acres and blended liability while the screen printed the
    // planted split, one policy reading two ways depending how you looked.
    // Per POLICY, because that is the grain the AIP settles a credit at.
    {
      const _esc = (v) => String(v == null ? "" : v).replace(/[&<>"]/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      // Same assembly as the screen, from the SAME rule — the overlays live in
      // `scoEcoEndorsements`, not `approvedYields`, and reading only the base
      // collection billed Anchor $11,967 over 2 fee lines against a statement of
      // $15,439 over 4. A printed sheet that disagrees with the screen above it
      // is the defect this page has already had once.
      let _billHTML = "";
      billingRowsForTab({
        approvedYields: farmer.approvedYields,
        scoEcoEndorsements: farmer.scoEcoEndorsements,
        crops: yields.map(a => a.crop), cropYear,
      }).forEach(_g => {
        const _b = buildBillingRule({ rows: _g.rows, claims: farmer.claims, policyId: _g.policyId,
          policyNumber: _g.policyNumber, cropYear, farmerPremium: _g.farmerPremium });
        if (!_b.hasAnything) return;
        const _rows = _g.rows;
        const _byClaim = new Map();
        _b.credits.forEach(c => {
          const k = c.claimNumber || "—";
          if (!_byClaim.has(k)) _byClaim.set(k, { claimNumber: c.claimNumber, crop: c.crop, amount: 0 });
          _byClaim.get(k).amount += c.amount;
        });
        _billHTML += `<tr><td colspan="2" style="font-weight:800; padding-top:8px">${_esc((_rows[0] || {}).policyNumber || "Policy")}</td></tr>
          <tr><td class="muted">Farmer premium <span style="font-size:7pt">gross &minus; subsidy</span></td><td style="text-align:right">${d0(_b.farmerPremium)}</td></tr>`;
        if (_b.fees > 0) _billHTML += `<tr><td class="muted">Administrative fees <span style="font-size:7pt">${_b.feeLines.length} billed line${_b.feeLines.length === 1 ? "" : "s"}${isCurrentUserAdmin() ? " · RMA schedule, not in the AIP file" : ""}</span></td><td style="text-align:right">+${d0(_b.fees)}</td></tr>`;
        [..._byClaim.values()].forEach(g => {
          _billHTML += `<tr><td class="muted">Claim credit applied <span style="font-size:7pt">${g.claimNumber ? `claim ${_esc(g.claimNumber)} · ` : ""}${_esc(g.crop || "")}</span></td><td style="text-align:right">&minus;${d0(g.amount)}</td></tr>`;
        });
        _billHTML += `<tr><td class="muted">Balance due <span style="font-size:7pt">premium + fees &minus; credits</span></td><td style="text-align:right">${d0(_b.balanceDue)}</td></tr>`;
        // The printed sheet carries the payments too — it is what the grower
        // actually holds, and a sheet that stops at "balance due" while the
        // screen shows it settled is the disagreement this page has had before.
        const _o = policyOutstanding({ policyId: _g.policyId, policyNumber: _g.policyNumber,
          policyType: "crop", cropYear, billed: _b.balanceDue, payments: farmer.premiumPayments });
        if (_o.paid !== 0) _billHTML += `<tr><td class="muted">Paid <span style="font-size:7pt">${_o.paymentCount} payment${_o.paymentCount === 1 ? "" : "s"}${_o.lastPaymentDate ? ` &middot; latest ${_esc(_o.lastPaymentDate)}` : ""}</span></td><td style="text-align:right">&minus;${d0(_o.paid)}</td></tr>`;
        _billHTML += `<tr class="total-row"><td style="font-weight:800; text-transform:uppercase; font-size:8pt; letter-spacing:0.06em">${_o.state === "settled" ? "Settled" : _o.state === "credit" ? "Credit Balance" : "Balance Outstanding"} <span style="font-weight:400; text-transform:none; letter-spacing:0; font-size:7pt">${_o.paid === 0 ? "nothing received against this policy yet" : "after payments received"}</span></td><td class="highlight" style="text-align:right">${d0(_o.outstanding)}</td></tr>`;
      });
      if (_billHTML) {
        bodyHTML += `<div class="county-block">
          <div class="county-header"><span class="county-dot"></span>${isCurrentUserAdmin() ? "What the AIP will bill" : "Your premium bill"} — per policy</div>
          <table><tbody>${_billHTML}</tbody></table></div>`;
      }
    }
  }

  // ── DRP ──
  if (section === "drp") {
    // Mirror DRPView's deriveQuarter: trust a clean "Q# YYYY" value first
    // (M13/NAU imports store this and often leave end_date null), then fall
    // back to parsing the end date. Parsing end_date alone — as this once did —
    // dumped every clean-quarter-but-null-end_date endorsement into "Unknown".
    const everyEnd = (farmer.drpPolicies||[]).flatMap(p =>
      (p.endorsements||[]).map(e => ({...e, quarter: (() => {
        if (e.quarter && /^Q[1-4]\s+\d{4}$/.test(String(e.quarter).trim())) {
          return String(e.quarter).trim();
        }
        if (!e.endDate) return "Unknown";
        let m, y;
        const iso = String(e.endDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
        const mdy = String(e.endDate).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (iso)      { y = parseInt(iso[1]); m = parseInt(iso[2]); }
        else if (mdy) { y = parseInt(mdy[3]); m = parseInt(mdy[1]); }
        else return "Unknown";
        return "Q" + (m<=3?1:m<=6?2:m<=9?3:4) + " " + y;
      })()}))
    );

    // Active-only: drop quarters that have fully ended. A quarter is active
    // when its last day (last day of the quarter's 3rd month) is today or later
    // — so the current quarter and all future quarters stay; expired ones go.
    // "Unknown" quarters (no parseable end date) are kept so real coverage is
    // never silently hidden by a data gap.
    const drpQuarterActive = (qLabel) => {
      const m = String(qLabel).match(/Q(\d)\s+(\d{4})/);
      if (!m) return true;
      const qn = parseInt(m[1]), yr = parseInt(m[2]);
      const qEnd = new Date(yr, qn*3, 0, 23, 59, 59, 999); // last day of quarter-end month
      const today = new Date(); today.setHours(0,0,0,0);
      return qEnd >= today;
    };
    const allEnds = everyEnd.filter(e => drpQuarterActive(e.quarter));

    // Live inputs that the on-screen DRP card uses for its indemnity / YAF / %
    // insured figures — pulled here so the PDF mirrors the screen exactly.
    //  • Forward months: RMA daily futures-implied prices (same source DRPView uses).
    //  • Milk-per-cow: NASS monthly series for the farmer's state → YAF on settled quarters.
    //  • Est. production: persisted on the farmer for the % Insured bar.
    //  • Price overrides default to none (session-only manual edits aren't here).
    const rmaForward = await fetchRmaForwardPrices();
    // the daily SILAS market estimate fills months RMA no longer prices (same merge as the screen)
    const monthEst = await fetchDrpMonthEstimates();
    const cmeFutures = drpMergeForwardPrices(rmaForward, monthEst);
    const fwdPrevPrint = drpMergeForwardPrices(rmaForward, monthEst, true);
    // RMA yield adjustment per endorsement — the same loader and rule the screen uses.
    const yctxPrint = await drpLoadYieldContext(allEnds, (farmer.state || "NY").toUpperCase());
    const drpEstProd = farmer.metadata?.drpEstProd || {};
    const priceOverrides = {};

    const totLiab = drpSum(allEnds, "liability");
    const totProd = drpSum(allEnds, "producerPremium");
    const totMilk = drpSum(allEnds, "declaredMilkProd");

    const qMap = {};
    allEnds.forEach(e => { const k = e.quarter||"Unknown"; if (!qMap[k]) qMap[k]=[]; qMap[k].push(e); });
    const quarters = Object.keys(qMap).sort((a,b) => drpSortKey(a)-drpSortKey(b));

    // Quarter range string
    const qRange = quarters.length > 0
      ? quarters[0] + (quarters.length > 1 ? " – " + quarters[quarters.length-1] : "")
      : "";

    statRowHTML = `
      <div class="stat-row">
        <div class="stat"><div class="stat-val">${allEnds.length}</div><div class="stat-lbl">Active Endorsements</div></div>
        <div class="stat"><div class="stat-val">${d0(totLiab)}</div><div class="stat-lbl">Total Liability</div></div>
        <div class="stat"><div class="stat-val">${d0(totProd)}</div><div class="stat-lbl">Producer Premium</div></div>
        <div class="stat"><div class="stat-val">${drpFmtM(totMilk)} lbs</div><div class="stat-lbl">Total Milk Covered</div></div>
        ${qRange ? `<div class="stat"><div class="stat-val" style="font-size:13pt">${qRange}</div><div class="stat-lbl">Quarter Range</div></div>` : ""}
      </div>`;

    // Empty-state: every quarter on file has already ended.
    if (!allEnds.length) {
      bodyHTML = `<div style="padding:40px 0;text-align:center;color:#60735f;font-size:12pt">
        No active DRP quarters — all endorsements on file have ended.
      </div>`;
    }

    quarters.forEach(q => {
      const rows  = qMap[q];
      const III   = rows.filter(e => e.classIII===1||e.classIII===true);
      const IV    = rows.filter(e => !(e.classIII===1||e.classIII===true));
      const IIIlbs = drpSum(III, "declaredMilkProd");
      const IVlbs  = drpSum(IV,  "declaredMilkProd");
      const totlbs = IIIlbs + IVlbs;
      const qProd  = drpSum(rows, "producerPremium");

      // Weighted averages — same as screen
      const IIIavgCov  = drpWA(III, "coverageLevel");
      const IIIavgTrig = drpWA(III, "triggerPrice");
      const IIIavgPrem = III.length && IIIlbs > 0 ? drpSum(III,"producerPremium")/IIIlbs*100 : null;
      const IIInetFloor = (IIIavgTrig != null && IIIavgPrem != null) ? IIIavgTrig - IIIavgPrem : null;

      const IVavgCov   = drpWA(IV, "coverageLevel");
      const IVavgTrig  = drpWA(IV, "triggerPrice");
      const IVavgPrem  = IV.length && IVlbs > 0 ? drpSum(IV,"producerPremium")/IVlbs*100 : null;
      const IVnetFloor = (IVavgTrig != null && IVavgPrem != null) ? IVavgTrig - IVavgPrem : null;

      // Est. indemnity — mirrors the on-screen DRP card exactly: USDA announced
      // prices + RMA/market estimates for unsettled months, adjusted by RMA's
      // own yield adjustment factor per endorsement.
      //
      // ⚠ A CLAIM ESTIMATE IS AN INTERNAL ADMIN TOOL. This sheet is reachable from the
      // portal (FarmerView's Export PDF), so every estimate is computed ONLY for an
      // admin — `showEstPrint` is the EFFECTIVE identity, false in the portal and in
      // preview-as. A null estimate renders nothing below: no chip, no class line, no
      // "includes RMA est.", no day-change note. The agent's own PDF is unchanged.
      const actuals   = drpGetQuarterActualsEnhanced(q, priceOverrides, cmeFutures);
      const withYieldAdjPrint = (e) => drpWithYieldAdj(e, yctxPrint, actuals);
      const estQ        = showEstPrint ? drpEstimateIndemnity(rows, actuals, withYieldAdjPrint) : null;
      const estIIIQ     = showEstPrint ? drpEstimateIndemnity(III, actuals, withYieldAdjPrint) : null;
      const estIVQ      = showEstPrint ? drpEstimateIndemnity(IV, actuals, withYieldAdjPrint) : null;
      const estIndemnity = estQ ? estQ.amount : null;
      // the day's change on the market estimate — same rule as the screen
      const prevActuals = showEstPrint && actuals?.silasEstimated ? drpGetQuarterActualsEnhanced(q, priceOverrides, fwdPrevPrint) : null;
      const prevComparable = !!(prevActuals && fwdPrevPrint.estAsOf && !prevActuals.isFuture
        && prevActuals.monthsTotalIII === actuals.monthsTotalIII && prevActuals.monthsTotalIV === actuals.monthsTotalIV);
      const estNote = (cur, list) => {
        if (!showEstPrint || !actuals?.silasEstimated) return null;
        const prev = prevComparable ? (drpEstimateIndemnity(list, prevActuals, withYieldAdjPrint)?.amount ?? null) : null;
        return drpDescribeEstimate({ asOf: actuals.estAsOf, prevAsOf: fwdPrevPrint.estAsOf, change: prev != null && cur != null ? cur - prev : null, carriedMonths: actuals.carriedMonths, futuresMonths: actuals.futuresMonths });
      };

      // RMA yield adjustment for the chip subline (same rule as the screen).
      const yieldSum = drpQuarterYieldSummary(rows.map(withYieldAdjPrint));
      const yafAvg = yieldSum.yafAvg;

      // % Insured vs. est. production (from the persisted drpEstProd) — same as screen.
      const perQ      = drpEstProd.__perQuarter === true;
      const estProdVal = perQ ? (drpEstProd[q] || '') : (drpEstProd.__global || '');
      const estNum    = parseFloat((estProdVal + '').replace(/,/g, ''));
      const pctInsured = estNum > 0 && totlbs > 0 ? totlbs / estNum * 100 : null;

      // Est. Indemnity header cell (matches screen: ⚡ CME, * partial, Y = YAF applied)
      const indemSuffix = (actuals?.hasEstimates ? " ⚡" : actuals?.isPartial ? " *" : "") + (yafAvg != null ? " Y" : "");
      const indemCell = !estQ ? "" : estIndemnity == null ? `
        <div class="q-stat">
          <span class="q-stat-lbl" style="color:#60735f">Est. Indemnity</span>
          <span class="q-stat-val" style="color:#60735f">Not priced yet</span>
          <span class="q-sub" style="color:#60735f">${drpUnpricedReason(actuals)}</span>
        </div>` : `
        <div class="q-stat">
          <span class="q-stat-lbl" style="color:${estIndemnity>0?"#7a4a00":"#4a7a4a"}">Est. Indemnity${indemSuffix}</span>
          <span class="q-stat-val" style="color:${estIndemnity>0?"#b85000":"#2d5a2d"}">${estIndemnity>0?"$"+Math.round(estIndemnity).toLocaleString():"— No trigger"}</span>
          ${estQ.partial ? `<span class="q-sub" style="color:#7a4a00">${estQ.priced} of ${estQ.priced + estQ.unpriced} endorsements priced</span>` : ""}
          ${actuals?.rmaEstimated ? `<span class="q-sub" style="color:#b85000">includes RMA est.</span>` : ""}
          ${actuals?.silasEstimated ? `<span class="q-sub" style="color:#b85000">${estNote(estIndemnity, rows)}</span>` : ""}
          ${yafAvg != null ? `<span class="q-sub" style="color:${yafAvg<1?"#2d5a2d":"#7a4a00"}">YAF ${yafAvg.toFixed(4)} (RMA)</span>` : ""}
          ${yieldSum.pendingText ? `<span class="q-sub" style="color:#60735f">${yieldSum.pendingText}</span>` : ""}
          ${yieldSum.unknownText ? `<span class="q-sub" style="color:#a33">${yieldSum.unknownText}</span>` : ""}
        </div>`;

      // % Insured header cell — III/IV split bar, matching the screen.
      const insuredCell = pctInsured == null ? "" : `
        <div class="q-stat" style="min-width:150px">
          <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:3px">
            <span class="q-stat-lbl">% Insured</span>
            <span style="font-family:monospace;font-weight:800;font-size:10pt;color:#1a2e1a">${pctInsured.toFixed(1)}%</span>
          </div>
          <div class="pct-track">
            <div class="pct-fill" style="width:${Math.min(IIIlbs/estNum*100,100)}%;background:#1a4a7a"></div>
            <div class="pct-fill" style="width:${Math.min(IVlbs/estNum*100,100)}%;background:#4a8a4a"></div>
          </div>
          <div style="display:flex;gap:8px;margin-top:3px">
            ${IIIlbs>0?`<span style="font-size:8pt;color:#1a4a7a;font-weight:700">III ${(IIIlbs/estNum*100).toFixed(1)}%</span>`:""}
            ${IVlbs>0?`<span style="font-size:8pt;color:#4a8a4a;font-weight:700">IV ${(IVlbs/estNum*100).toFixed(1)}%</span>`:""}
          </div>
        </div>`;

      const classBlock = (label, cls, lbs, avgCov, avgTrig, avgPrem, netFloor, estObj, color, bg) => {
        if (!cls.length) return "";
        const estIndem = estObj ? estObj.amount : null;
        const indemStr = !estObj ? ""
          : estIndem == null
            ? `<div class="class-indem" style="color:#60735f">Est. Indemnity: Not priced yet — ${drpUnpricedReason(actuals, label === "CLASS III" ? "III" : "IV")}</div>`
          : estIndem > 0
            ? `<div class="class-indem" style="color:#b85000">Est. Indemnity: $${Math.round(estIndem).toLocaleString()}</div>`
            : `<div class="class-indem" style="color:#2d5a2d">No trigger — $0</div>`;
        const note = estIndem == null ? null : estNote(estIndem, cls);
        const noteStr = note ? `<div class="q-sub" style="color:#b85000">${note}</div>` : "";
        return `
          <div class="class-block" style="border-color:${color}33; background:${bg}">
            <div class="class-header" style="color:${color}">${label} — ${cls.length} endorsement${cls.length>1?"s":""} · ${drpFmtM(lbs)} lbs</div>
            <div class="class-stats">
              <div class="cs-item">
                <div class="cs-lbl">Wtd Avg Coverage</div>
                <div class="cs-val" style="color:${color}">${avgCov!=null?(avgCov*100).toFixed(0)+"%":"—"}</div>
              </div>
              <div class="cs-item">
                <div class="cs-lbl">Wtd Avg Trigger Price</div>
                <div class="cs-val" style="color:${color}">${avgTrig!=null?"$"+avgTrig.toFixed(2)+"/cwt":"—"}</div>
              </div>
              <div class="cs-item">
                <div class="cs-lbl">Wtd Avg Premium</div>
                <div class="cs-val" style="color:${color}">${avgPrem!=null?"$"+avgPrem.toFixed(4)+"/cwt":"—"}</div>
              </div>
              <div class="cs-item cs-highlight" style="background:${color}; border-color:${color}">
                <div class="cs-lbl" style="color:rgba(255,255,255,0.7)">Est. Net Floor</div>
                <div class="cs-val" style="color:#fff; font-size:14pt">${netFloor!=null?"$"+netFloor.toFixed(2)+"/cwt":"—"}</div>
              </div>
            </div>
            ${indemStr}${noteStr}
          </div>`;
      };

      bodyHTML += `
        <div class="q-card">
          <div class="q-header">
            <span class="q-badge">${q}</span>
            <div class="q-stat"><span class="q-stat-lbl">Total Lbs</span><span class="q-stat-val">${drpFmtM(totlbs)}</span></div>
            ${IIIlbs>0?`<div class="q-stat"><span class="q-stat-lbl" style="color:#1a4a7a">III Lbs</span><span class="q-stat-val" style="color:#1a4a7a">${drpFmtM(IIIlbs)}</span></div>`:""}
            ${IVlbs>0?`<div class="q-stat"><span class="q-stat-lbl" style="color:#2d5a2d">IV Lbs</span><span class="q-stat-val" style="color:#2d5a2d">${drpFmtM(IVlbs)}</span></div>`:""}
            <div class="q-stat"><span class="q-stat-lbl">Producer Prem</span><span class="q-stat-val">$${Number(qProd).toLocaleString()}</span></div>
            ${indemCell}
            ${insuredCell}
          </div>
          <div class="q-body">
            ${classBlock("CLASS III", III, IIIlbs, IIIavgCov, IIIavgTrig, IIIavgPrem, IIInetFloor, estIIIQ, "#1a4a7a", "#f0f5fc")}
            ${classBlock("CLASS IV",  IV,  IVlbs,  IVavgCov,  IVavgTrig,  IVavgPrem,  IVnetFloor,  estIVQ,  "#2d5a2d", "#f0f5f0")}
          </div>
        </div>`;
    });

    // Extra CSS for DRP quarter cards
    css += `
      .q-card { border: 1px solid #e4ece4; border-radius: 8px; overflow: hidden; margin-bottom: 16px; page-break-inside: avoid; }
      .q-header { display: flex; align-items: center; gap: 20px; padding: 12px 18px; background: #f4f8f4; border-bottom: 1px solid #e4ece4; flex-wrap: wrap; }
      .q-badge { background: #e8f4e8; color: #2d4a2d; font-size: 12pt; font-weight: 800; padding: 4px 14px; border-radius: 5px; border: 1px solid #2d4a2d33; }
      .q-stat { display: flex; flex-direction: column; }
      .q-stat-lbl { font-size: 7.5pt; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #60735f; }
      .q-stat-val { font-family: monospace; font-weight: 800; font-size: 13pt; color: #1a2e1a; }
      .q-sub { font-size: 7pt; font-weight: 700; margin-top: 1px; }
      .pct-track { height: 8px; border-radius: 4px; background: #e4ece4; overflow: hidden; width: 150px; display: flex; }
      .pct-fill { height: 100%; }
      .q-body { padding: 14px 18px; display: flex; flex-direction: column; gap: 12px; }
      .class-block { border: 1px solid; border-radius: 7px; padding: 12px 14px; }
      .class-header { font-size: 8.5pt; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 10px; }
      .class-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
      .cs-item { background: #fff; border: 1px solid #ddd; border-radius: 5px; padding: 7px 10px; }
      .cs-highlight { border-radius: 5px; padding: 7px 10px; }
      .cs-lbl { font-size: 7pt; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #60735f; margin-bottom: 3px; }
      .cs-val { font-family: monospace; font-weight: 800; font-size: 12pt; color: #1a2e1a; }
      .class-indem { margin-top: 8px; font-size: 10pt; font-weight: 800; font-family: monospace; }
    `;
  }

  // ── LRP ──
  if (section === "lrp") {
    // ⚠ AN LRP END DATE IS A CALENDAR DAY, NOT AN INSTANT (2026-11-03). `new Date("2026-06-26")`
    // is midnight UTC, the evening of June 25 anywhere in the US, so every end date here
    // printed a day early (Sparta's Jun 26 line read "Jun 25") and a line ending on the
    // 1st landed in the month before. Read the day as local midnight.
    const lrpEndDay = (v) => { const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v || NaN); };
    // Admin-only (canSeeLrpIndemnity): this sheet prints from the portal farmer card,
    // so a grower's copy carries no Indemnity column at all.
    const showLrpIndemPrint = canSeeLrpIndemnity();
    const today = new Date();
    const allEnds = (farmer.lrpPolicies||[]).flatMap(pol =>
      (pol.endorsements||[]).map(e => ({...e, commodity: e.commodity||pol.commodity||"Cattle"}))
    );
    const active = allEnds.filter(e => { const d2=lrpEndDay(e.endDate); return !isNaN(d2)&&d2>=today; }).sort((a,b)=>lrpEndDay(a.endDate)-lrpEndDay(b.endDate));
    const ended  = allEnds.filter(e => { const d2=lrpEndDay(e.endDate); return isNaN(d2)||d2<today; }).sort((a,b)=>lrpEndDay(b.endDate)-lrpEndDay(a.endDate));
    const totalPrem = allEnds.reduce((s,e)=>s+(e.producerPremium||0),0);
    const activeHead = active.reduce((s,e)=>s+(e.head||0),0);

    statRowHTML = `
      <div class="stat-row">
        <div class="stat"><div class="stat-val">${allEnds.length}</div><div class="stat-lbl">Endorsements</div></div>
        <div class="stat"><div class="stat-val">${activeHead.toLocaleString()}</div><div class="stat-lbl">Active Head</div></div>
        <div class="stat"><div class="stat-val">${d(totalPrem)}</div><div class="stat-lbl">Total Premium</div></div>
      </div>`;

    const renderLRPTable = (rows, title) => {
      if (!rows.length) return "";
      return `<div class="section-title">${title}</div>
        <table>
          <thead><tr>
            <th>Commodity</th><th>End Date</th><th>Head</th><th>Target Wt.</th>
            <th>Coverage Price</th><th>Actual End Val.</th><th>Producer Prem.</th>${showLrpIndemPrint ? '<th>Indemnity</th>' : ''}
          </tr></thead>
          <tbody>
          ${rows.map(e => {
            const indem = e.payableIndemnity > 0 ? e.payableIndemnity
              : (e.actualEndValue > 0 && e.actualEndValue < e.coveragePrice)
                ? (e.coveragePrice - e.actualEndValue) * (e.head||0) * (e.targetWeight||1) : null;
            return `<tr>
              <td>${e.commodity||"—"}</td>
              <td>${e.endDate ? lrpEndDay(e.endDate).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—"}</td>
              <td>${f(e.head)}</td>
              <td>${e.targetWeight ? f(e.targetWeight,2)+" cwt" : "—"}</td>
              <td class="highlight">${d(e.coveragePrice)}</td>
              <td>${e.actualEndValue ? d(e.actualEndValue) : '<span class="muted">pending</span>'}</td>
              <td>${d(e.producerPremium)}</td>
              ${showLrpIndemPrint ? `<td>${indem ? d0(indem) : '<span class="muted">—</span>'}</td>` : ''}
            </tr>`;
          }).join("")}
          </tbody>
        </table>`;
    };

    // Monthly summary renderer — mirrors the on-screen MonthlySummaryTable but
    // as a print-friendly HTML table. Sums head, coverage value, premium, and
    // indemnity by end-date month so dairy farmers can see "how many calves
    // protected in July" at a glance instead of a long row-per-endorsement list.
    const renderLRPMonthlyTable = (rows, title, isActive) => {
      if (!rows.length) return "";
      const groups = {};
      for (const e of rows) {
        const dt = e.endDate ? lrpEndDay(e.endDate) : null;
        if (!dt || isNaN(dt)) continue;
        const key = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`;
        if (!groups[key]) groups[key] = {
          key,
          label: dt.toLocaleDateString('en-US', { month:'long', year:'numeric' }),
          count: 0, head: 0, cov: 0, prod: 0, gros: 0, indem: 0, commodities: new Set(),
        };
        const g = groups[key];
        g.count++;
        g.head += e.head || 0;
        g.cov  += (e.coveragePrice||0) * (e.head||0) * (e.targetWeight||1);
        g.prod += e.producerPremium || 0;
        g.gros += e.premium || 0;
        if (e.payableIndemnity > 0) g.indem += e.payableIndemnity;
        else if (e.actualEndValue > 0 && e.actualEndValue < e.coveragePrice) {
          g.indem += (e.coveragePrice - e.actualEndValue) * (e.head||0) * (e.targetWeight||1);
        }
        if (e.commodity) g.commodities.add(e.commodity);
      }
      const months = Object.values(groups).sort((a,b) => isActive ? a.key.localeCompare(b.key) : b.key.localeCompare(a.key));
      if (months.length === 0) return "";
      const totals = {
        count: months.reduce((s,m)=>s+m.count,0),
        head:  months.reduce((s,m)=>s+m.head,0),
        cov:   months.reduce((s,m)=>s+m.cov,0),
        prod:  months.reduce((s,m)=>s+m.prod,0),
        gros:  months.reduce((s,m)=>s+m.gros,0),
        indem: months.reduce((s,m)=>s+m.indem,0),
      };
      return `<div class="section-title">${title} — by End Date Month</div>
        <table>
          <thead><tr>
            <th>End Month</th><th>Commodity</th><th>Endorsements</th><th>Head</th>
            <th>Coverage Value</th><th>Producer Prem.</th><th>Gross Prem.</th>
            ${(isActive || !showLrpIndemPrint) ? '' : '<th>Indemnity</th>'}
          </tr></thead>
          <tbody>
          ${months.map(m => `<tr>
            <td><strong>${m.label}</strong></td>
            <td>${[...m.commodities].join(' + ') || '—'}</td>
            <td>${m.count}</td>
            <td><strong>${m.head.toLocaleString()}</strong></td>
            <td class="highlight">${d0(m.cov)}</td>
            <td>${d0(m.prod)}</td>
            <td>${d0(m.gros)}</td>
            ${(isActive || !showLrpIndemPrint) ? '' : `<td>${m.indem > 0 ? d0(m.indem) : '<span class="muted">—</span>'}</td>`}
          </tr>`).join("")}
          <tr class="total-row" style="background:#1a2e1a;color:#fff;font-weight:800;">
            <td>TOTAL</td>
            <td></td>
            <td>${totals.count}</td>
            <td>${totals.head.toLocaleString()}</td>
            <td>${d0(totals.cov)}</td>
            <td>${d0(totals.prod)}</td>
            <td>${d0(totals.gros)}</td>
            ${(isActive || !showLrpIndemPrint) ? '' : `<td>${d0(totals.indem)}</td>`}
          </tr>
          </tbody>
        </table>`;
    };

    // Read the current view selection set by LRPView (sessionStorage).
    let lrpView = 'detail';
    try { lrpView = sessionStorage.getItem('cloverag_lrp_endorsement_view') || 'detail'; } catch {}
    if (lrpView === 'monthly') {
      bodyHTML += renderLRPMonthlyTable(active, "Active Endorsements", true);
      bodyHTML += renderLRPMonthlyTable(ended,  "Ended Endorsements",  false);
    } else {
      bodyHTML += renderLRPTable(active, "Active Endorsements");
      bodyHTML += renderLRPTable(ended,  "Ended Endorsements");
    }
  }

  // ── PRF ──
  if (section === "prf") {
    // ⚠ ONE CROP YEAR ONLY, AND IT IS THE YEAR IN FORCE — see prfCoverageState(). One stat
    // row per policy, so it never summed two years into one figure the way the
    // card did — but it printed the prior year's block with no year on it,
    // beside the current one, on the sheet the grower actually holds.
    const _prfScope = prfCoverageState(farmer.prfPolicies, prfCoverageYear());
    // ⚠ NO CURRENT COVERAGE IS A STATEMENT, NOT AN EMPTY SECTION. A farm whose only
    // PRF policy is a prior year prints that in as many words on the sheet the grower
    // holds, with the year named — never a caption reading "Showing crop year null",
    // and never the prior year's block standing in for this one.
    if (!_prfScope.hasCurrent) {
      bodyHTML += `<div style="font-size:11px;color:#8a5a00;margin:4px 0 8px;font-weight:700">` +
        `No current PRF coverage for ${_prfScope.coverageYear}.</div>`;
      if (_prfScope.priorYears.length)
        bodyHTML += `<div style="font-size:10px;color:#60735f;margin:0 0 8px">` +
          `Prior PRF coverage on file: crop year${_prfScope.priorYears.length > 1 ? "s" : ""} ` +
          `${_prfScope.priorYears.join(", ")} — history, not coverage in force.</div>`;
      if (_prfScope.renewalYears.length)
        bodyHTML += `<div style="font-size:10px;color:#60735f;margin:0 0 8px">` +
          `Renewal on file for ${_prfScope.renewalYears.join(", ")} — not yet in force.</div>`;
      if (_prfScope.hasUnknownYear)
        bodyHTML += `<div style="font-size:10px;color:#8a5a00;margin:0 0 8px">` +
          `${_prfScope.unknownYear.length} PRF polic${_prfScope.unknownYear.length === 1 ? "y" : "ies"} on file with no readable crop year ` +
          `— not counted as coverage in force.</div>`;
    } else if (_prfScope.priorYears.length || _prfScope.renewalYears.length || _prfScope.hasUnknownYear) {
      bodyHTML += `<div style="font-size:10px;color:#60735f;margin:4px 0 8px">` +
        `Showing crop year ${_prfScope.year}. ` +
        (_prfScope.priorYears.length
          ? `Prior year${_prfScope.priorYears.length > 1 ? "s" : ""} on file (not shown): ${_prfScope.priorYears.join(", ")}. ` : "") +
        (_prfScope.renewalYears.length
          ? `Renewal on file for ${_prfScope.renewalYears.join(", ")}, not yet in force.` : "") +
        (_prfScope.hasUnknownYear
          ? ` ${_prfScope.unknownYear.length} polic${_prfScope.unknownYear.length === 1 ? "y" : "ies"} with no readable crop year, not counted.` : "") +
        `</div>`;
    }
    _prfScope.policies.forEach(policy => {
      const intervals = policy.intervals||[];
      const totLiab = intervals.reduce((s,e)=>s+(Number(e.liability)||0),0);
      const intervalPremSum2 = intervals.reduce((s,e)=>s+(Number(e.premium)||0),0);
      const totPrem = policy.totalPremium ?? intervalPremSum2;
      const subsidy = policy.subsidy ?? null;
      const netPrem = policy.netPremium ?? (subsidy != null ? totPrem - subsidy : null);
      const gridMap = {};
      intervals.forEach(iv => { const g=`${iv.gridId||"Unknown"}|${iv.county||""}`; if (!gridMap[g]) gridMap[g]=[]; gridMap[g].push(iv); });
      const totalAcres = policy.totalAcres > 0 ? policy.totalAcres : Object.values(gridMap).reduce((s,ivs)=>{
        return s+(Number(ivs[0]?.totalGridAcres)||Number(ivs[0]?.insuredAcres)||0);
      },0);

      statRowHTML += `
        <div class="stat-row" style="margin-bottom:6px">
          <div class="stat"><div class="stat-val">${totalAcres>0?f(totalAcres,0)+" ac":"—"}</div><div class="stat-lbl">Total Acres</div></div>
          <div class="stat"><div class="stat-val">${d0(totLiab)}</div><div class="stat-lbl">Total Liability</div></div>
          <div class="stat"><div class="stat-val">${d0(totPrem)}</div><div class="stat-lbl">Gross Premium</div></div>
          <div class="stat"><div class="stat-val">${netPrem!=null?d0(netPrem):"—"}</div><div class="stat-lbl">Net Premium</div></div>
          <div class="stat"><div class="stat-val">${subsidy!=null?d0(subsidy):"—"}</div><div class="stat-lbl">Subsidy</div></div>
          <div class="stat"><div class="stat-val">${policy.coverageLevel?pct(policy.coverageLevel):"—"}</div><div class="stat-lbl">Coverage</div></div>
        </div>`;

      bodyHTML += `<div class="section-title">Intervals by Grid</div>
        <table>
          <thead><tr>
            <th>Grid ID</th><th>County</th><th>Interval</th><th>Insured Acres</th><th>% Value</th><th>Prod. Factor</th><th>Liability</th><th>Premium</th>
          </tr></thead>
          <tbody>`;
      Object.entries(gridMap).forEach(([gridKey, ivs]) => {
        const [displayGridId, gridCounty] = gridKey.split('|');
        ivs.forEach((iv, idx) => {
          bodyHTML += `<tr>
            ${idx===0 ? `<td rowspan="${ivs.length}">${displayGridId}</td><td rowspan="${ivs.length}">${gridCounty || iv.county || "—"}</td>` : ""}
            <td>${iv.intervalCode||"—"}</td>
            <td>${iv.insuredAcres ? f(iv.insuredAcres,2) : "—"}</td>
            <td>${iv.percentValue ? (iv.percentValue*100).toFixed(0)+"%" : "—"}</td>
            <td>${iv.productivityFactor ? f(iv.productivityFactor,2) : "—"}</td>
            <td class="highlight">${d0(iv.liability)}</td>
            <td>${d(iv.premium)}</td>
          </tr>`;
        });
      });
      bodyHTML += `<tr class="total-row"><td colspan="5">Total</td><td class="highlight">${d0(totLiab)}</td><td>${d0(totPrem)}</td></tr>`;
      bodyHTML += `</tbody></table>`;
    });
  }

  // ── PREVENTED PLANTING SUMMARY ──
  // One-page customer-facing refresher + their specific PP eligibility numbers.
  // Educational copy in left column, per-county PP table on the right, FPD
  // callout strip across the top sourced from farmer.state (+ county for PA's
  // I-80 soy FPD split). Math comes from the same ppPerAcreForRow / maxAcres
  // helpers the in-app PP tab uses, so the printout never drifts from the UI.
  if (section === "pp") {
    const cropYear = farmer.cropYear || 2026;
    const allYields = farmer.approvedYields || [];
    const yields = allYields.filter(a => !FALL_CROPS.includes((a.crop||"").toUpperCase()));
    const pdfProcBeansPrice = parseFloat(farmer.metadata?.processingBeansPrice) || 0;
    const pdfPrices = pdfProcBeansPrice > 0 ? repriceMap(prices, { "PROCESSING BEANS": pdfProcBeansPrice }) : prices;

    const ppElig = dedupPPRows(farmer.ppEligibility || []).filter(p =>
      !FALL_CROPS.includes((p.crop||"").toUpperCase())
    );

    const cornFPD = getCornFPD(farmer.state) || "see policy";
    const soyFPD  = getSoyFPD(farmer.state, farmer.county) || "see policy";

    // Section-specific CSS — newspaper-style: FPD pill row, full-width PP table,
    // educational refresher flows in 3 balanced columns below. Avoids the dead
    // whitespace problem the side-by-side layout had.
    css += `
      .fpd-row { margin: 8px 0 14px; }
      .fpd-callout { display:inline-flex; gap:12px; align-items:center; flex-wrap:wrap; background:#f0f6f0; border:1.5px solid #4a9a4a; border-radius:8px; padding:9px 16px; }
      .fpd-callout .fpd-label { font-size:9.5pt; font-weight:800; letter-spacing:0.08em; text-transform:uppercase; color:#2d5a10; }
      .fpd-callout .fpd-pill { background:#fff; border:1.5px solid #4a9a4a; border-radius:6px; padding:4px 12px; font-weight:800; color:#1a2e1a; font-size:10pt; display:inline-block; }
      .fpd-callout .fpd-pill .crop-lbl { color:#4a7a4a; font-weight:700; margin-right:6px; font-size:8.5pt; letter-spacing:0.06em; display:inline-block; }
      .fpd-callout .fpd-pill .fpd-date { display:inline-block; }
      /* Counties stack vertically — full-width tables always render cleanly,
         no matter how many counties a farmer has. Side-by-side flex layout
         collapsed columns once the dollar values got wide. */
      .pp-counties { margin-bottom: 6px; }
      .pp-counties .pp-county { margin-bottom: 8px; }
      .pp-counties .pp-county:last-child { margin-bottom: 0; }
      .pp-counties .pp-county table { font-size: 10pt; max-width: none; margin: 0; width: 100%; }
      .pp-counties .pp-county th, .pp-counties .pp-county td { padding: 5px 12px; }
      .pp-counties .pp-county .county-header { font-size: 10.5pt; margin-bottom: 3px; }
      .pp-formula-note { font-size: 8pt; color:#60735f; font-style: italic; margin: 2px 0 12px; }
      .pp-edu-cols { column-count: 2; column-gap: 26px; column-fill: balance; font-size: 9pt; line-height: 1.38; }
      .pp-edu-cols p { margin: 0 0 6px; break-inside: avoid-column; -webkit-column-break-inside: avoid; page-break-inside: avoid; }
      .pp-edu-cols strong { color: #1a2e1a; font-weight: 800; }
      .pp-edu-cols .em-new { background: #fff4d8; padding: 0 3px; border-radius: 2px; color: #6b4f00; font-weight: 800; }
    `;

    statRowHTML = `
      <div class="stat-row">
        <div class="stat"><div class="stat-val">${ppElig.length}</div><div class="stat-lbl">PP Eligible Crops</div></div>
        <div class="stat"><div class="stat-val">${[...new Set(ppElig.map(p => p.county))].filter(Boolean).length}</div><div class="stat-lbl">Counties</div></div>
        <div class="stat"><div class="stat-val">${f(ppElig.reduce((s,p) => s + (maxAcres(p)||0), 0), 1)} ac</div><div class="stat-lbl">Total Eligible Acres</div></div>
      </div>`;

    // FPD callout pill — inline-flex so it hugs its content. Dates are wrapped
    // in their own span (rather than bare text nodes) so html2canvas-based PDF
    // capture renders them; without the span the dates were silently dropped.
    bodyHTML += `
      <div class="fpd-row">
        <div class="fpd-callout">
          <span class="fpd-label">Your ${cropYear} Final Planting Dates · ${farmer.state || "—"}:</span>
          <span class="fpd-pill"><span class="crop-lbl">CORN</span><span class="fpd-date">${cornFPD}</span></span>
          <span class="fpd-pill"><span class="crop-lbl">SOYBEANS</span><span class="fpd-date">${soyFPD}</span></span>
        </div>
      </div>`;

    // ── PP ELIGIBILITY TABLE — full width, flex-wrap for multi-county ──
    bodyHTML += `<div class="section-title">Your ${cropYear} PP Eligibility</div>`;

    if (ppElig.length === 0) {
      bodyHTML += `<p style="color:#60735f;font-style:italic;padding:14px;background:#f8faf8;border-radius:6px;font-size:10pt;">No PP Eligibility Report on file. Contact our office for your eligibility numbers.</p>`;
    } else {
      bodyHTML += `<div class="pp-counties">`;
      const ppCounties = [...new Set(ppElig.map(p => p.county))].filter(Boolean);
      ppCounties.forEach(county => {
        const cRows = ppElig.filter(p => p.county === county);
        const tableRows = cRows.map(pp => {
          const _pCUp  = (pp.county||"").toUpperCase().trim();
          const _pCrUp = (pp.crop||"").toUpperCase();
          const _pTUp  = (pp.type||"").toUpperCase();
          const ppGeneric = isGenericPPRow(pp.type, pp.practice);
          const ayBase = yields.filter(a =>
            (a.county||"").toUpperCase().trim() === _pCUp &&
            (a.crop||"").toUpperCase() === _pCrUp &&
            (ppGeneric || !_pTUp || (a.type||"").toUpperCase() === _pTUp)
          );
          const ay = ayBase.find(a => normalizePractice(a.practice).toUpperCase() === normalizePractice(pp.practice).toUpperCase())
            || ayBase.find(a => !(a.practice||"") && !(pp.practice||""))
            || ayBase[0] || null;
          const _ppb = ppPerAcreForRow(pp, ay, pdfPrices, cropYear,
            ay && _unitsByKey ? _unitsByKey[cuKey(ay.county, ay.crop, ay.plan, ay.typeCode, ay.practiceCode)] : null);
          const perAcre = _ppb.perAcre;
          const maxAc   = maxAcres(pp);
          return { ...pp, ay, perAcre, ppf: _ppb.ppf, maxAc };
        }).filter(r => r.maxAc > 0 || r.perAcre != null);

        if (tableRows.length === 0) return;

        bodyHTML += `<div class="county-block pp-county">
          <div class="county-header"><span class="county-dot"></span>${county} County</div>
          <table>
            <thead><tr>
              <th style="text-align:left">Crop / Type</th>
              <th style="text-align:right">Max Elig. Acres</th>
              <th style="text-align:right">PP Guar / Acre</th>
            </tr></thead>
            <tbody>`;
        tableRows.forEach(r => {
          bodyHTML += `<tr>
            <td style="font-weight:700">${r.crop||"—"}${r.type ? ` · ${r.type}` : ""}${normalizePractice(r.practice) === "Organic" ? " · Organic" : ""}</td>
            <td style="text-align:right; font-family:monospace; font-weight:800">${r.maxAc > 0 ? f(r.maxAc,1) : '<span class="muted">—</span>'}</td>
            <td style="text-align:right" class="highlight">${r.perAcre != null ? `<span style="font-weight:800">${r.ppf && !r.ppf.exact ? "at least " : ""}${d(r.perAcre)}</span>` : '<span class="muted">—</span>'}</td>
          </tr>`;
        });
        bodyHTML += `</tbody></table></div>`;
      });
      bodyHTML += `</div>`; // close pp-counties
      bodyHTML += `<div class="pp-formula-note">Per-acre guarantee = APH × Spring Price × MPCI Coverage × PP Factor. Eligible acres reflect the 1-in-4 rule on your APH database.</div>`;
    }

    // ── EDUCATIONAL REFRESHER — 2-column newspaper layout ──
    // 2 cols instead of 3 gives wider, more readable paragraphs and avoids
    // mid-sentence column splits. Less-common topics (added land, new
    // breaking) are folded into a single combined "special cases" paragraph
    // and the cover-crop bullets are inlined as a prose run, so the whole
    // refresher fits one landscape letter page comfortably.
    bodyHTML += `<div class="section-title">Prevented Planting — Quick Refresher</div>`;
    bodyHTML += `
      <div class="pp-edu-cols">
        <p><strong>The basics.</strong> If an insurable cause of loss (excess moisture, drought, etc.) prevents you from planting your insured crop by the Final Planting Date (FPD), Prevent Plant pays a percentage of what your full crop guarantee would have been. PP factors vary by crop — corn is 55%, soybeans 60%. The loss must be general to your area, not unique to your operation.</p>

        <p><strong>Notify us within 72 hours.</strong> Reach out within 72 hours of the FPD if you don't intend to plant during the Late Planting Period (LPP), or within 72 hours of realizing you can't get in during the LPP. Late notice can void the claim.</p>

        <p><strong>FSA reporting — after June 10, before June 25.</strong> Per FSA: prevented planting must be reported to your local FSA office <strong>after June 10</strong> but <strong>before June 25</strong>. Both deadlines apply.</p>

        <p><strong>The 20/20 rule.</strong> PP requires at least 20 acres OR 20% of the unit's insurable acreage prevented — whichever is less. <strong>Small wet holes count:</strong> the minimum is measured at the <strong>unit level</strong>, not per field — twenty 1-acre wet spots scattered across your unit add up to 20 acres of PP and qualify. PP acres inside a partially-planted field are treated as the same crop you planted around them.</p>

        <p><strong>Late plant vs. PP — your choice.</strong> Each day late inside the LPP reduces your <strong>per-acre guarantee</strong> by 1% — your coverage level itself doesn't change, only the dollar value of the guarantee for those late-planted acres. Example (from R&amp;H's own materials): a $700/ac guarantee loses $7/ac per day, so 25 days late = $525/ac. You're not required to take PP just because you're delayed.</p>

        <p><strong>The 1-in-4 rule.</strong> Acreage must have been planted, harvested (or had a documented insurable loss) using good farming practices in at least one of the last four crop years. <span class="em-new">NEW for 2026:</span> the requirement that the historical crop be <em>insured</em> has been removed. <span class="em-new">Also NEW for 2026:</span> buy-up PP has been eliminated — everyone is on the standard factor.</p>

        <p><strong>Land eligibility — special cases.</strong> First-year new breaking and first-year CRP release are <em>not</em> PP-eligible until they meet the 1-in-4 test. If you've added cropland this year (purchased, leased, inherited) and no cause of loss had occurred when you took possession, your eligible PP acres scale up by the ratio of current cropland to prior-year cropland.</p>

        <p><strong>Cover crops on PP ground.</strong> A cover crop not harvested for grain or seed keeps your <strong>full PP payment</strong> — hay, graze, or silage anytime. Harvesting it for grain or seed drops PP to <strong>$0</strong> if planted before LPP ends, or cuts it <strong>65%</strong> if planted after. A volunteer crop harvested for grain or seed denies the PP entirely.</p>

        <p><strong>Plant a second crop on PP ground.</strong> PP payment drops to <strong>35%</strong> and APH on those acres falls to 60% for next year — unless you have qualifying double-cropping history for those exact acres.</p>
      </div>`;
  }

  // ── ASSEMBLE + OPEN PRINT WINDOW ────────────────────────────────────────────
  // Two output paths so mobile + desktop both work cleanly:
  //   • 📄 Download PDF — html2pdf via cdnjs, produces a real .pdf file that
  //     the iOS/Android share sheet treats as a file (textable, emailable).
  //   • 🖨 Print / Save as PDF — native browser print, best on desktop where
  //     "Save as PDF" is a destination option in the print dialog.
  // No auto-print for PP — user picks. Other sections keep auto-print since
  // that's Kevin's existing screenshot workflow.
  const safeName = (farmer.name||"Summary").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_");
  const pdfFilename = `${safeName}_${section.toUpperCase()}_Summary_${farmer.cropYear||""}.pdf`;
  const autoPrint = section !== "pp";

  const fullHTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${farmer.name} — ${sectionLabels[section]||section} — ${farmer.cropYear||""}</title>
  <style>${css}</style>
</head>
<body>
  ${headerHTML}
    ${statRowHTML}
    </div>
    <div class="header-logo"><img src="${logoSrc}" alt="Clover Ag Risk Management" /></div>
  </div>
  <div class="body">
    <div class="no-print" style="padding:10px 16px;margin-bottom:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
      <button id="dl-pdf-btn" onclick="downloadPDF()" style="background:#1a2e1a;color:#fff;border:none;border-radius:8px;padding:10px 18px;font-size:14px;font-weight:700;cursor:pointer">📄 Download PDF</button>
      <button onclick="window.print()" style="background:#fff;color:#2d5a10;border:1.5px solid #2d5a10;border-radius:8px;padding:10px 18px;font-size:14px;font-weight:700;cursor:pointer">🖨 Print</button>
      <span id="dl-status" style="color:#60735f;font-size:12px;margin-left:6px;"></span>
    </div>
    ${bodyHTML}
  </div>
  ${footerHTML}
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"><\/script>
  <script>
    // Wait for ALL images (logo) to fully load before any actions.
    window.onload = function() {
      var imgs = Array.prototype.slice.call(document.images);
      var done = imgs.map(function(img) {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise(function(r) { img.onload = r; img.onerror = r; });
      });
      Promise.all(done).then(function() {
        ${autoPrint ? "setTimeout(function() { window.print(); }, 250);" : ""}
      });
    };

    // Real PDF download — produces a .pdf file the mobile share sheet treats
    // as a file (not a URL link). ignoreElements drops the toolbar from the
    // captured output. scale: 3 keeps text crisp.
    window.downloadPDF = function() {
      var statusEl = document.getElementById('dl-status');
      var btn = document.getElementById('dl-pdf-btn');
      if (typeof html2pdf === 'undefined') {
        if (statusEl) statusEl.textContent = 'PDF library still loading — try again in a moment.';
        return;
      }
      if (btn) { btn.disabled = true; btn.style.opacity = '0.6'; }
      if (statusEl) statusEl.textContent = 'Generating PDF…';
      var opt = {
        margin: 0.4,
        filename: ${JSON.stringify(pdfFilename)},
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 3,
          useCORS: true,
          logging: false,
          ignoreElements: function(el) { return (el.classList && el.classList.contains('no-print')) || el.id === 'silas-back-bar' || el.id === 'silas-back-bar-spacer'; }
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
        // Without this, html2canvas renders one tall canvas and html2pdf slices
        // it at arbitrary pixel rows — cutting paragraphs and table rows in half
        // wherever the content spills to a 2nd page. 'css' honors our
        // page-break-* rules (.page-break, .pp-county); 'legacy' adds the
        // selector list so a break is forced *before* any of these elements
        // rather than through the middle of one.
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['tr', '.county-block', '.pp-county', '.pp-edu-cols p', '.section-title', '.fpd-callout', '.stat-row', '.footer']
        }
      };
      html2pdf().set(opt).from(document.body).save().then(function() {
        if (btn) { btn.disabled = false; btn.style.opacity = '1'; }
        if (statusEl) statusEl.textContent = 'Done.';
        setTimeout(function() { if (statusEl) statusEl.textContent = ''; }, 2000);
      }).catch(function(err) {
        if (btn) { btn.disabled = false; btn.style.opacity = '1'; }
        if (statusEl) statusEl.textContent = 'PDF failed: ' + (err && err.message ? err.message : 'unknown error');
      });
    };
  <\/script>
</body>
</html>`;

  // Use a Blob URL instead of document.write — modern browser popup security
  // sometimes rejects document.write into a freshly-opened about:blank window
  // (especially in restricted / embedded contexts). Loading a Blob URL is a
  // normal navigation and works in every popup environment.
  try {
    const blob = new Blob([withPrintBackBar(fullHTML)], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank", "width=1100,height=800");
    if (!win) {
      console.error("generatePolicyPDF: window.open returned null (popup blocked?)");
      alert("Popup blocked — please allow popups for this site and try again.");
      URL.revokeObjectURL(url);
      return;
    }
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
  } catch (err) {
    console.error("generatePolicyPDF: failed to open popup:", err);
    alert("Failed to open print window: " + (err && err.message ? err.message : "unknown error"));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CLAIMS VIEW (Farmer Card Tab)
// ─────────────────────────────────────────────────────────────────────────────
function ClaimsView({ farmer, isMobile }) {
  const claims = farmer.claims || [];
  if (claims.length === 0) return null;

  const sorted = [...claims].sort((a, b) => new Date(b.checkDate || 0) - new Date(a.checkDate || 0));
  const totalIndemnity = sorted.reduce((s, c) => s + (c.indemnityAmount || 0), 0);

  return (
    <div>
      <Card style={{ marginBottom:16 }}>
        <div style={{ display:"flex", gap:20, flexWrap:"wrap", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:10, color:"#60735f", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em" }}>Indemnity Payments</div>
            <div style={{ fontSize:24, fontWeight:800, color:"#1a2e1a" }}>{sorted.length}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:"#60735f", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em" }}>Total Amount</div>
            <div style={{ fontSize:24, fontWeight:800, color:totalIndemnity > 0 ? "#c04030" : "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>
              ${totalIndemnity.toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 })}
            </div>
          </div>
        </div>
      </Card>

      {isMobile ? (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {sorted.map((c, i) => (
            <Card key={i} style={{ borderLeft:`4px solid ${c.status === "Paid" ? "#2d6a2d" : "#c86030"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <span style={{ fontWeight:800, fontSize:13, color:"#1a2e1a" }}>{c.description}</span>
                <Pill color={aipColor(c.aip)}>{aipLabel(c.aip)}</Pill>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 12px", fontSize:12 }}>
                <div><span style={{ color:"#60735f", fontSize:10, display:"block" }}>Date</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{claimDateCell(c, "Pending")}</span></div>
                <div><span style={{ color:"#60735f", fontSize:10, display:"block" }}>Amount</span><span style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#c04030" }}>${(c.indemnityAmount||0).toLocaleString(undefined,{minimumFractionDigits:2})}</span>
                  {/* Same vocabulary as the Claims page: every settled row says how it moved. */}
                  {(c.indemnityAmount||0) > 0 && (() => { const m = claimPayMethod(c.lossCode); return (
                    <span title={m.note} style={{ display:"block", fontSize:10, fontWeight:700, color:"#7a5b00", textTransform:"uppercase", letterSpacing:"0.04em" }}>{m.label}</span>
                  ); })()}
                </div>
                <div><span style={{ color:"#60735f", fontSize:10, display:"block" }}>Status</span><span style={{ fontWeight:700, color: c.status === "Paid" ? "#2d6a2d" : "#c86030" }}>{c.status}</span></div>
                <div><span style={{ color:"#60735f", fontSize:10, display:"block" }}>Claim #</span><span style={{ fontSize:11 }}>{c.claimNumber || "—"}</span></div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"#f4f8f4" }}>
                {["Date","Type","Description","Claim #","Policy","AIP","Status","Amount"].map(h => (
                  <th key={h} style={{ padding:"8px 14px", textAlign:h==="Amount"?"right":"left", fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#60735f", textTransform:"uppercase", borderBottom:"1px solid #e4ece4", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid #f0f4f0" }}>
                  <td style={{ padding:"10px 14px", fontVariantNumeric: "tabular-nums", fontSize:12 }}>{claimDateCell(c, "—")}</td>
                  <td style={{ padding:"10px 14px" }}>
                    <Pill color={c.policyType === "lrp" ? "#7a3b10" : c.policyType === "drp" ? "#1a4a7a" : c.policyType === "prf" ? "#3a3a7a" : "#2d4a2d"}>
                      {(c.policyType||"crop").toUpperCase()}
                    </Pill>
                  </td>
                  {/* lossCodeName used to be appended raw here, so an M/F premium
                      credit (absent from that map) read as a bare "— M". The method
                      now sits under the Amount, in the Claims-page wording. */}
                  <td style={{ padding:"10px 14px", fontWeight:600, color:"#1a2e1a" }}>{c.description}</td>
                  <td style={{ padding:"10px 14px", fontSize:12, color:"#60735f", fontVariantNumeric: "tabular-nums" }}>{c.claimNumber || "—"}</td>
                  <td style={{ padding:"10px 14px", fontSize:12, color:"#60735f" }}>{c.policyNumber}</td>
                  <td style={{ padding:"10px 14px" }}><Pill color={aipColor(c.aip)}>{aipLabel(c.aip)}</Pill></td>
                  <td style={{ padding:"10px 14px" }}>
                    {(() => {
                      // Paid = cleared check (green). Verified = AIP confirmed indemnity
                      // but check hasn't cleared yet — surface as a confident blue, NOT
                      // the orange Pending color (these are real money on the way).
                      // Adjustment = small ride-along, amber. Pending = unknown.
                      const s = c.status;
                      const styles = s === "Paid" ? { bg:"#e8f6ec", fg:"#2d6a2d" }
                        : s === "Verified" ? { bg:"#e7f0fa", fg:"#1a4a7a" }
                        : s === "Adjustment" ? { bg:"#fdf6e7", fg:"#7a5b00" }
                        : { bg:"#fdf1e8", fg:"#c86030" };
                      return <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:700, whiteSpace:"nowrap", background: styles.bg, color: styles.fg }}>{s}</span>;
                    })()}
                  </td>
                  <td style={{ padding:"10px 14px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#c04030", fontSize:14 }}>
                    ${(c.indemnityAmount||0).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}
                    {(c.indemnityAmount||0) > 0 && (() => { const m = claimPayMethod(c.lossCode); return (
                      <div title={m.note} style={{ fontSize:10, fontWeight:700, color:"#7a5b00", fontFamily:"inherit", textTransform:"uppercase", letterSpacing:"0.04em" }}>{m.label}</div>
                    ); })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FARMER DETAIL VIEW
// ─────────────────────────────────────────────────────────────────────────────
// John Deere machine-data field table (overlay). Two sources — JD MACHINE (from
// Ops Center) + FSA keyed acres (crop_coverage) — with per-field, per-county, and
// machine-vs-keyed comparison views. Machine county acres are grid-cell-derived
// (actual coverage, deduped), so border-straddling fields split by county.
function JdConnectionsView({ farmers = [], onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [repulling, setRepulling] = useState(null);
  const [recompFarm, setRecompFarm] = useState(null);
  const [mapBuild, setMapBuild] = useState(null); // { orgId, cached } while force-building a farm's yield maps
  const [pick, setPick] = useState({});
  const [recomp, setRecomp] = useState(null);
  const [copied, setCopied] = useState(null);   // "link" | "msg" | invite id — brief copied confirmation
  // Farmer-BOUND invite: binds the customer before the grower ever clicks, so the
  // org lands linked instead of being name-matched afterward.
  const [invFarmer, setInvFarmer] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invBusy, setInvBusy] = useState(false);
  const [invOut, setInvOut] = useState(null);   // { url, farmer, emailed }
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState(null);     // { adopted[], actionable[], linked_count, total }
  const [setup, setSetup] = useState(null);     // { orgId, step } while one-click setup runs
  // Day to day this screen needs two controls: find new connections, and set a farm
  // up. Everything else is a repair tool — re-authorize, raw dump, whole-book
  // recompute, force a re-pull. Showing all nine at once is what made a solved
  // procedure feel like an investigation every time. Repairs live behind this.
  const [adv, setAdv] = useState(false);
  // asJson lives at module scope — every direct fetch site in this file has the
  // same plain-text-error problem, not just this panel.
  const inviteMsg = (lnk) => `Hi — to connect your John Deere data to Clover Ag, open this on a device where you're logged into your OWN John Deere Operations Center account:\n\n${lnk}\n\nOn the "Connect with Clover Ag" screen: check the box next to your farm, turn on BOTH "Locations & Boundaries" AND "Field Operations / Field Analyzer" data, then hit Save. That's it — thanks!`;
  const copy = (text, which) => {
    try { navigator.clipboard.writeText(text); setCopied(which); setTimeout(() => setCopied(null), 1800); }
    catch (e) { window.prompt("Copy:", text); }
  };
  const load = () => {
    setData(null); setErr(null);
    authedFetch(`/api/market?fn=jd-orgs`).then(r => r.json())
      .then(d => { if (d && d.error) setErr(d.error); else setData(d); })
      .catch(e => setErr(String((e && e.message) || e)));
  };
  useEffect(load, []); // eslint-disable-line
  const farmerOpts = [...farmers].filter(f => f && f.id && f.name).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const link = async (org) => {
    const fid = pick[org.id]; if (!fid) return;
    setBusy(org.id);
    try {
      const j = await asJson(await authedFetch(`/api/market?fn=jd-adopt&org_id=${org.id}&farmer_id=${fid}&org_name=${encodeURIComponent(org.name || "")}`, { method: "POST" }));
      if (j && j.error) alert(j.error); else load();
    } catch (e) { alert(String((e && e.message) || e)); }
    setBusy(null);
  };
  // Manually re-run the sync for one linked farmer (retries a failed connection on
  // demand — the sync is otherwise daily-cron only). Scoped to the org's farmer so
  // it stays fast and can't stall on the whole book.
  const syncNow = async (org) => {
    if (!org.farmer_id) return;
    setSyncing(org.id);
    try {
      const r = await authedFetch(`/api/market?fn=jd-sync&farmer=${org.farmer_id}`, { method: "POST" });
      const j = await asJson(r);
      const mine = (j && j.synced || []).find(s => s && (s.org === org.name || s.org_id === org.id)) || (j && j.synced || [])[0];
      if (mine && mine.error) alert(`Sync failed: ${mine.error}`);
      else if (j && j.error) alert(j.error);
      load();
    } catch (e) { alert(String((e && e.message) || e)); }
    setSyncing(null);
  };
  // FULL re-pull for one farmer — picks up EDITS a grower made in Ops Center that a
  // normal sync can't see (it skips operations it already has). The main case is a
  // crop RELABEL (a wheat map they'd mislabeled as corn): full=1 re-fetches every op
  // and the backend propagates the corrected crop onto the stored grids + rollups
  // (attribution is geometry-based, so no re-bin needed). Also refreshes moisture /
  // variety / dates. Use this after a grower fixes something on their end.
  const repull = async (org) => {
    if (!org.farmer_id) return;
    setRepulling(org.id);
    try {
      const r = await authedFetch(`/api/market?fn=jd-sync&farmer=${org.farmer_id}&full=1`, { method: "POST" });
      const j = await asJson(r);
      const mine = (j && j.synced || []).find(s => s && (s.org === org.name || s.org_id === org.id)) || (j && j.synced || [])[0];
      if (mine && mine.error) { alert(`Re-pull failed: ${mine.error}`); setRepulling(null); return; }
      if (j && j.error) { alert(j.error); setRepulling(null); return; }
      // Also fire a gridding pass + re-attribution for this farm, so any op that
      // only NOW exposes a yield export (e.g. one the grower just re-processed in
      // Ops Center) gets its map built, and the CLU rollups refresh. Grid building
      // itself finishes on the background cron over the next cycle.
      let pc = null;
      try { pc = await asJson(await authedFetch(`/api/market?fn=jd-precompute&farmer=${org.farmer_id}`, { method: "POST" })); } catch (e) {}
      try { await authedFetch(`/api/market?fn=jd-county-backfill&farmer=${org.farmer_id}`, { method: "POST" }); } catch (e) {}
      alert(`Re-pulled ${org.name}.\nOperations: ${mine?.operations ?? "?"} · crop relabels applied: ${mine?.crop_relabels ?? 0}${pc ? `\nMaps: ${pc.cached ?? "?"} built, ${pc.fired ?? 0} newly queued, ${pc.building ?? 0} in progress` : ""}\n\nCorrected crop labels are live now. Any newly-queued maps finish building over the next ~15–30 min. If a map still never appears, John Deere isn't exposing a downloadable yield layer for that operation.`);
      load();
    } catch (e) { alert(String((e && e.message) || e)); }
    setRepulling(null);
  };
  // Re-split ONE farmer's stored yield maps by CLU ownership — same as the whole-book
  // recompute but scoped, so it finishes in seconds. Use after a boundary/logic change
  // affects a single farm (footprint acres, physical county, new CLUs).
  const recompFarmer = async (org) => {
    if (!org.farmer_id || recompFarm) return;
    setRecompFarm(org.id);
    let offset = 0, done = 0, total = null, dedup = null;
    try {
      for (let i = 0; i < 40; i++) {
        const r = await authedFetch(`/api/market?fn=jd-county-backfill&reftf=1&n=100&farmer=${org.farmer_id}&offset=${offset}`, { method: "POST" });
        const j = await asJson(r);
        if (j && j.error) { alert("Recompute failed: " + j.error); break; }
        if (j && j.deduped) dedup = j.deduped; // reported on the first page only
        done += (j && j.processed) || 0;
        total = (j && j.total != null) ? j.total : total;
        offset = (j && j.next_offset) || offset;
        if (!j || !j.remaining) break;
      }
      const dedupMsg = dedup ? `\nDuplicate fields merged: ${dedup.duplicates || 0}${dedup.replants ? ` · replants flagged: ${dedup.replants}` : ""}` : "";
      alert(`Recomputed ${org.name}: ${done}/${total ?? "?"} yield maps re-attributed (CLU ownership, footprint acres, physical county).${dedupMsg}`);
      load();
    } catch (e) { alert(String((e && e.message) || e)); }
    setRecompFarm(null);
  };
  // Force-build a farm's yield maps NOW instead of waiting for the background cron to
  // rotate to it — a big new farm (Kludt ~2,300 ops) would otherwise take days, and
  // Machine vs FSA / By area stay empty until the maps exist. Loops the (farmer-scoped,
  // high-throughput) precompute until nothing is left firing/building.
  const buildMaps = async (org) => {
    if (!org.farmer_id || mapBuild) return;
    setMapBuild({ orgId: org.id, cached: 0 });
    let last = -1, stall = 0;
    try {
      for (let i = 0; i < 120; i++) {
        const j = await asJson(await authedFetch(`/api/market?fn=jd-precompute&farmer=${org.farmer_id}`, { method: "POST" }));
        if (j && j.error) { alert("Build failed: " + j.error); break; }
        const cached = (j && j.cached) || 0;
        setMapBuild({ orgId: org.id, cached });
        const active = ((j && j.fired) || 0) + ((j && j.building) || 0) > 0;
        stall = cached === last ? stall + 1 : 0;
        last = cached;
        if (!active && stall >= 2) break; // nothing in flight and no new grids
        await new Promise((r) => setTimeout(r, 3000)); // let JD's exports finish preparing
      }
      alert(`${org.name}: ${last} yield maps built. Now click "↻ Recompute maps" to attribute them (county/CLU/footprint).`);
      load();
    } catch (e) { alert(String((e && e.message) || e)); }
    setMapBuild(null);
  };
  // Re-split every stored yield map by CLU ownership (whole book). Needed once
  // after the attribution logic changed, and any time CLU boundaries are added —
  // credits each insured the yield harvested on THEIR land, even by another
  // operator's combine. Pages via offset (reftf todo list is stable).
  const recomputeAttribution = async () => {
    if (recomp && recomp.running) return;
    if (!window.confirm("Re-split every stored John Deere yield map by CLU ownership?\n\nThis credits each insured the yield harvested on their own fields — even when another operator's combine ran it. Takes a minute or two; safe to run anytime.")) return;
    setRecomp({ running: true, done: 0, total: null });
    let offset = 0, done = 0, total = null;
    try {
      for (let i = 0; i < 80; i++) {
        const r = await authedFetch(`/api/market?fn=jd-county-backfill&reftf=1&n=60&offset=${offset}`, { method: "POST" });
        const j = await asJson(r);
        if (j && j.error) { alert("Recompute failed: " + j.error); setRecomp({ running: false, done }); return; }
        done += (j && j.processed) || 0;
        total = (j && j.total != null) ? j.total : total;
        offset = (j && j.next_offset) || offset;
        setRecomp({ running: true, done, total });
        if (!j || !j.remaining) break;
      }
    } catch (e) { alert(String((e && e.message) || e)); setRecomp({ running: false, done }); return; }
    setRecomp({ running: false, done, total });
    alert(`Done — re-split ${done} operations by CLU ownership. Each insured now sees the yield harvested on their own land, on their own maps.`);
  };
  // The one button to press when a farm says Connected on John Deere's side but
  // isn't here. Fresh token (a stale one CANNOT see an org that granted us after it
  // was minted), re-probe every org, adopt whatever is newly readable, and report in
  // words. Replaces "wait up to 3h for the sweep, then paste JSON to Claude".
  const runCheck = async () => {
    if (checking) return;
    setChecking(true); setCheck(null);
    try {
      const j = await asJson(await authedFetch(`/api/market?fn=jd-orgs&probe=1&fresh=1&adopt=1`));
      if (j && j.error) alert(j.error);
      else if (j && j.ok === false) alert(j.note || "Couldn't reach John Deere.");
      else { setCheck(j); setData(j); }   // j IS a full orgs payload — reuse it, no second round trip
    } catch (e) { alert(String((e && e.message) || e)); }
    setChecking(false);
  };
  // Connected != usable. A new farm needs a sync, then its yield maps built, then
  // those maps attributed to CLUs — in that order, because each step consumes the
  // one before. Running them as three separate buttons is how farms ended up half
  // set up, and how "it's connected" kept meaning something different to each of
  // us. One button, correct order, and it says which step it's on.
  const setUpFarm = async (org) => {
    if (!org.farmer_id || setup) return;
    const F = `farmer=${encodeURIComponent(org.farmer_id)}`;
    setSetup({ orgId: org.id, step: "Pulling operations…" });
    try {
      // A FIRST PULL ON A BIG FARM DOES NOT FIT IN ONE INVOCATION, so this drives
      // it across as many as it takes. The sync now returns `partial: true` when it
      // hits its own deadline, having FLUSHED every field batch it finished, so a
      // further pass genuinely resumes rather than starting over.
      //
      // ⚠ THE OLD VERSION OF THIS LOOP COUNTED REPETITION AND CALLED IT PROGRESS.
      // It printed "Pulling fields… (pass 3)", which reads as two passes' worth of
      // work banked. It was not: the sync wrote nothing until after its whole field
      // loop, so a killed pass saved NOTHING and pass 3 re-ran pass 1 byte for byte.
      // On swedefarms (525 fields) that counter ran while zero operations landed,
      // for two days. So the label now reports what is DONE — fields walked and
      // operations stored — and never a pass number, because a pass number measures
      // our retrying and not the farm's data.
      //
      // Two exits, and the difference is the point. `partial` with the numbers still
      // moving is a big farm, and we keep going. `partial` with NOTHING moving twice
      // running is a stall, and it stops and says where, instead of spinning to a cap.
      let mine = null, walked = -1, stalled = 0;
      for (let pass = 1; pass <= 40; pass++) {
        try {
          const s = await asJson(await authedFetch(`/api/market?fn=jd-sync&${F}`, { method: "POST" }));
          if (s && s.error) throw new Error(s.error);
          mine = ((s && s.synced) || []).find(x => x && (x.org === org.name || x.org_id === org.id)) || ((s && s.synced) || [])[0];
          if (mine && mine.error) throw new Error(mine.error);
          if (!mine || !mine.partial) break;      // ran to the end of the field list
          const done = mine.fields_done || 0, got = mine.new || 0;
          setSetup({ orgId: org.id, step: `Pulling operations… ${done}/${mine.fields || "?"} fields, ${got} new` });
          // Progress is EITHER frontier: a pass can walk hundreds of already-stored
          // fields and bank no new operation, and a pass can bank operations without
          // reaching further than the last one did. Only neither, twice, is a stall.
          stalled = (done > walked || got > 0) ? 0 : stalled + 1;
          if (done > walked) walked = done;
          if (stalled >= 2) throw new Error(`This farm stopped making progress at ${done} of ${mine.fields || "?"} fields.\n\nEverything pulled so far is saved. Check the farm is still sharing Field Operations in Ops Center, then try again.`);
        } catch (e) {
          const msg = String((e && e.message) || e);
          if (!/took too long|HTTP 50[24]|timed? ?out/i.test(msg)) throw e;
          if (pass === 40) throw new Error(`${msg}\n\nThis farm has a lot of history — everything pulled so far is saved. Press "Set up this farm" again to keep going.`);
        }
      }
      const ops = mine && mine.operations != null ? mine.operations : null;
      const fields = mine && mine.fields != null ? mine.fields : null;
      // Yield maps are prepared by John Deere asynchronously, so this polls until
      // nothing is in flight AND the count stops moving — not a fixed number of tries.
      let cached = 0, last = -1, stall = 0;
      for (let i = 0; i < 120; i++) {
        setSetup({ orgId: org.id, step: `Building maps… ${cached}` });
        const j = await asJson(await authedFetch(`/api/market?fn=jd-precompute&${F}`, { method: "POST" }));
        if (j && j.error) throw new Error(j.error);
        cached = (j && j.cached) || 0;
        const active = (((j && j.fired) || 0) + ((j && j.building) || 0)) > 0;
        stall = cached === last ? stall + 1 : 0;
        last = cached;
        if (!active && stall >= 2) break;
        await new Promise(r => setTimeout(r, 3000));
      }
      setSetup({ orgId: org.id, step: "Attributing acres…" });
      let offset = 0, done = 0;
      for (let i = 0; i < 40; i++) {
        const j = await asJson(await authedFetch(`/api/market?fn=jd-county-backfill&reftf=1&n=100&${F}&offset=${offset}`, { method: "POST" }));
        if (j && j.error) throw new Error(j.error);
        done += (j && j.processed) || 0;
        offset = (j && j.next_offset) || offset;
        if (!j || !j.remaining) break;
      }
      alert(`${org.name} is live.\n\n`
        + `${fields != null ? `Fields: ${fields}\n` : ""}`
        + `${ops != null ? `Operations: ${ops}\n` : ""}`
        + `Yield maps built: ${cached}\nMaps attributed: ${done}\n\n`
        + (ops === 0
            ? "No operations came back — this org has no planting or harvest records in Ops Center yet."
            : "Open the farmer's Field Data tab to see it."));
      load();
    } catch (e) {
      alert(`Setup stopped: ${String((e && e.message) || e)}\n\nEverything that finished is saved — run it again and it picks up where it left off.`);
    }
    setSetup(null);
  };
  // Mint (or re-issue) a farmer-bound invite. Re-sending returns the SAME link, so
  // a grower never ends up holding two live ones.
  const makeInvite = async (send) => {
    if (send && !invEmail.trim()) { alert("Enter an email address to send to."); return; }
    if (invBusy) return;
    setInvBusy(true); setInvOut(null);
    try {
      // THE CUSTOMER PICKER CHOOSES THE PATH, because the two links do different
      // things and only one of them works for every grower.
      //
      // With a customer -> jd-invite: the grower signs in and we hold a token in
      // THEIR name. That is the only way to read a farm outside Spartan's partner
      // hierarchy — Panek granted the app and their org still is not among the 26
      // the agency login can see, and never will be. It also binds the customer,
      // so the farm lands on the right book row.
      //
      // Without one -> jd-sendlink: John Deere's own grant screen, no token. Fine
      // for a grower already in the hierarchy (Austic, Bridgewater, Red Arrow all
      // landed this way), useless for anyone who isn't.
      const fn = invFarmer ? "jd-invite" : "jd-sendlink";
      const q = `${invFarmer ? `farmer=${encodeURIComponent(invFarmer)}` : ""}${send ? `${invFarmer ? "&" : ""}email=${encodeURIComponent(invEmail.trim())}` : ""}`;
      const j = await asJson(await authedFetch(`/api/market?fn=${fn}${q ? `&${q}` : ""}`, { method: "POST" }));
      if (j && j.error) { alert(j.error); }
      else {
        setInvOut(j);
        if (send && j.emailed && !j.emailed.sent) alert(`Link created, but the email didn't send: ${j.emailed.error}\n\nCopy the link below and send it yourself.`);
        load();
      }
    } catch (e) { alert(String((e && e.message) || e)); }
    setInvBusy(false);
  };
  const cancelInvite = async (inv) => {
    if (!window.confirm(`Cancel the John Deere invite for ${inv.farmer_name || "this farmer"}?\n\nThe link they were sent stops working immediately.`)) return;
    try {
      const j = await asJson(await authedFetch(`/api/market?fn=jd-invite&farmer=${encodeURIComponent(inv.farmer_id)}&revoke=1`, { method: "POST" }));
      if (j && j.error) alert(j.error); else { setInvOut(null); load(); }
    } catch (e) { alert(String((e && e.message) || e)); }
  };
  const th = (t, r) => <th style={{ textAlign: r ? "right" : "left", padding:"7px 10px", fontSize:10.5, letterSpacing:"0.05em", color:"#60735f", borderBottom:"2px solid #e0e8e0", whiteSpace:"nowrap" }}>{t}</th>;
  const td = (v, extra) => <td style={{ padding:"7px 10px", fontSize:12.5, color:"#2d3f2d", borderBottom:"1px solid #eef2ee", ...extra }}>{v}</td>;
  const pill = (bg, fg, t) => <span style={{ background:bg, color:fg, fontSize:10.5, fontWeight:800, padding:"2px 8px", borderRadius:10 }}>{t}</span>;
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:5000, background:"rgba(20,32,20,0.55)", display:"flex", justifyContent:"center", alignItems:"flex-start", overflow:"auto", padding:"3vh 2vw" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:12, maxWidth:900, width:"100%", boxShadow:"0 12px 48px rgba(0,0,0,0.3)", overflow:"hidden" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", background:"#1a2e1a", color:"#fff" }}>
          <div style={{ fontWeight:800, fontSize:16 }}>🚜 John Deere Connections</div>
          <span onClick={onClose} style={{ cursor:"pointer", fontSize:22, fontWeight:700, lineHeight:1 }}>×</span>
        </div>
        <div style={{ padding:"10px 18px", fontSize:12, color:"#60735f", borderBottom:"1px solid #eef2ee", display:"flex", gap:14, flexWrap:"wrap", alignItems:"center" }}>
          <span><b style={{ color:"#2d6a2d" }}>Sharing</b> = grower approved data sharing to Clover Ag.</span>
          <span><b style={{ color:"#2d4a2d" }}>Linked</b> = mapped to a farmer in the book.</span>
          {data && data.total != null && <span style={{ marginLeft:"auto", fontWeight:700 }}><span style={{ color:"#2d6a2d" }}>{data.linked_count} connected</span> · {data.total} visible</span>}
          <button disabled={checking} onClick={runCheck}
            title="Mint a fresh John Deere token, re-read every org, link anything newly readable, and say in plain English what each farm is waiting on. This is the first thing to click when a farm says Connected on John Deere's side but isn't here."
            style={{ border:"1px solid #2d5a10", borderRadius:6, background: checking ? "#eef2ee" : "#2d5a10", padding:"4px 10px", fontSize:11.5, fontWeight:800, cursor: checking ? "default" : "pointer", color: checking ? "#60735f" : "#fff" }}>
            {checking ? "Checking…" : "🔄 Check for new connections"}
          </button>
          <button onClick={() => setAdv(a => !a)}
            title="Repair tools — only needed when Check can't fix it"
            style={{ border:"1px solid #cdd9cd", borderRadius:6, background:"#f6faf6", padding:"4px 10px", fontSize:11.5, fontWeight:700, cursor:"pointer", color:"#60735f" }}>
            {adv ? "Hide repair tools" : "Repair tools ▾"}
          </button>
        </div>
        {adv && (
          <div style={{ padding:"8px 18px", display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", background:"#fafcfa", borderBottom:"1px solid #eef2ee" }}>
            <span style={{ fontSize:11, color:"#60735f", marginRight:2 }}>Only when Check can't sort it:</span>
            <button onClick={load} style={{ border:"1px solid #cdd9cd", borderRadius:6, background:"#fff", padding:"4px 10px", fontSize:11.5, fontWeight:700, cursor:"pointer", color:"#2d4a2d" }}>↻ Refresh</button>
            <button onClick={async () => {
                try { const r = await authedFetch(`/api/market?fn=jd-authorize&json=1`); const j = await asJson(r); if (j && j.url) window.location.href = j.url; else alert((j && j.error) || "Could not start re-authorization."); }
                catch (e) { alert(String((e && e.message) || e)); }
              }}
              title="Sign back into the SHARED John Deere login. Only needed if Check reports a farm John Deere still won't show us — that means our token predates their grant."
              style={{ border:"1px solid #2d5a10", borderRadius:6, background:"#eaf5ea", padding:"4px 10px", fontSize:11.5, fontWeight:800, cursor:"pointer", color:"#2d5a10" }}>🔑 Re-authorize JD</button>
            <button onClick={async () => {
                try {
                  const j = await asJson(await authedFetch(`/api/market?fn=jd-orgs&probe=1&fresh=1`));
                  const txt = JSON.stringify(j && (j.summary || j), null, 1);
                  try { await navigator.clipboard.writeText(txt); alert("Raw diagnostic copied to clipboard."); }
                  catch (e) { window.prompt("Copy this:", txt); }
                } catch (e) { alert(String((e && e.message) || e)); }
              }}
              title="Raw dump of everything John Deere returns to our token."
              style={{ border:"1px solid #cdd9cd", borderRadius:6, background:"#fff", padding:"4px 10px", fontSize:11.5, fontWeight:700, cursor:"pointer", color:"#2d4a2d" }}>🔍 Raw dump</button>
            <button disabled={recomp && recomp.running} onClick={recomputeAttribution} title="Re-split EVERY stored yield map by CLU ownership, whole book. Needed after adding field boundaries — not for onboarding one farm." style={{ border:"1px solid #cdd9cd", borderRadius:6, background: recomp && recomp.running ? "#eef2ee" : "#fff", padding:"4px 10px", fontSize:11.5, fontWeight:700, cursor: recomp && recomp.running ? "default" : "pointer", color:"#2d4a2d" }}>{recomp && recomp.running ? `Recomputing… ${recomp.done}${recomp.total ? "/" + recomp.total : ""}` : "🗺️ Recompute whole book"}</button>
          </div>
        )}
        {/* Check result, in words. Every stuck connection has been one of five states;
            the cost was always telling them apart, never fixing them. */}
        {check && (
          <div style={{ margin:"12px 18px 0", padding:"12px 14px", borderRadius:10, background:"#f6faf6", border:"1px solid #cdd9cd" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <div style={{ fontWeight:800, fontSize:13, color:"#1a2e1a" }}>
                {check.adopted && check.adopted.length
                  ? `✓ Linked ${check.adopted.length} new farm${check.adopted.length > 1 ? "s" : ""}`
                  : "Checked — nothing new to link"}
              </div>
              <span style={{ marginLeft:"auto", fontSize:11, color:"#60735f" }}>fresh token · {check.linked_count} connected of {check.total} visible</span>
              <span onClick={() => setCheck(null)} style={{ cursor:"pointer", fontSize:16, color:"#60735f", lineHeight:1 }}>×</span>
            </div>
            {check.adopted && check.adopted.length > 0 && (
              <div style={{ fontSize:12, color:"#2d5a10", marginBottom:8, lineHeight:1.6 }}>
                {check.adopted.map((a, i) => (
                  <div key={i}>
                    <b>{a.org}</b>{a.matched ? ` → ${a.matched}` : " → no name match, pick the farmer below"}
                    {a.needs_share ? " (approved, but the data share isn't finished)" : ""}
                  </div>
                ))}
                <div style={{ marginTop:5, color:"#4a6e4a" }}>Now run <b>↻ Sync now</b> on each, then <b>🗺 Build maps</b>.</div>
              </div>
            )}
            {check.actionable && check.actionable.length > 0 ? (
              <div>
                <div style={{ fontSize:10.5, letterSpacing:"0.05em", color:"#60735f", fontWeight:800, margin:"4px 0 5px" }}>WAITING ON SOMEONE</div>
                {check.actionable.map((a, i) => (
                  <div key={i} style={{ fontSize:12, lineHeight:1.5, padding:"4px 0", borderTop: i ? "1px solid #eef2ee" : "none" }}>
                    <span style={{ background: a.who === "you" ? "#fff3d9" : "#eef2ee", color: a.who === "you" ? "#8a5a00" : "#5a7a5a", fontSize:10, fontWeight:800, padding:"1px 6px", borderRadius:8, marginRight:6 }}>
                      {a.who === "you" ? "YOU" : "GROWER"}
                    </span>
                    <b>{a.org}</b>{a.farmer ? ` (${a.farmer})` : ""} — {a.state}
                    <div style={{ color:"#60735f", fontSize:11.5, marginLeft:2 }}>{a.detail}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize:12, color:"#4a6e4a" }}>
                Nothing is waiting on you or a grower. If a farm still isn't here, John Deere isn't showing it to our login at all — that grower hasn't approved Clover Ag yet.
              </div>
            )}
          </div>
        )}
        {/* Invite a grower — the whole point of the push. One link, they self-connect.
            Pick the farmer FIRST: a bound invite lands on the right book row with no
            name matching, which is the difference between "connected" and "connected
            to whoever happens to share their spelling". */}
        {data && (
          <div style={{ margin:"12px 18px", padding:"12px 14px", borderRadius:10, background:"#eef6ea", border:"1px solid #cfe3c6" }}>
            <div style={{ fontWeight:800, fontSize:13, color:"#1a2e1a", marginBottom:4 }}>📨 Connect a grower</div>
            <div style={{ fontSize:11.5, color:"#4a6e4a", marginBottom:9, lineHeight:1.45 }}>
              <b>Pick the customer</b> and email them a link — that works for <b>every</b> grower, including ones with no connection to our
              Ops Center. They sign in with their own John Deere login, tick their farm, turn on <b>Locations &amp; Boundaries</b> and
              <b> Field Operations</b>, and save. You get an email when it lands.
              <div style={{ marginTop:4 }}>
                Leave the customer blank and you get the open link below instead — that only reaches growers already shared to us,
                so <b>pick the customer unless you know they're already in our Ops Center</b>.
              </div>
            </div>
            {/* The link itself, always visible. Nothing is generated: there is one
                link, it never expires, and it is safe to text, forward or post. */}
            <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", marginBottom:8 }}>
              <code style={{ flex:"1 1 300px", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", background:"#fff", border:"1px solid #cdd9cd", borderRadius:6, padding:"7px 9px", fontSize:11.5, color:"#2d4a2d" }}>
                {data.connect_link || "JD_CLIENT_ID not set"}
              </code>
              <button disabled={!data.connect_link} onClick={() => copy(data.connect_link, "link")}
                style={{ border:"none", borderRadius:6, padding:"7px 14px", fontSize:11.5, fontWeight:800, cursor:"pointer", background:"#2d5a10", color:"#fff", whiteSpace:"nowrap" }}>
                {copied==="link" ? "✓ Copied" : "Copy link"}
              </button>
              <button disabled={!data.connect_link} onClick={() => copy(inviteMsg(data.connect_link), "msg")}
                style={{ border:"1px solid #2d5a10", borderRadius:6, padding:"7px 14px", fontSize:11.5, fontWeight:800, cursor:"pointer", background:"#fff", color:"#2d5a10", whiteSpace:"nowrap" }}>
                {copied==="msg" ? "✓ Copied" : "Copy text message"}
              </button>
            </div>
            <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", marginBottom:8 }}>
              <select value={invFarmer} onChange={e => { setInvFarmer(e.target.value); setInvOut(null); }}
                style={{ flex:"1 1 200px", minWidth:0, fontSize:12, padding:"6px 8px", borderRadius:6, border:"1px solid #cdd9cd", background:"#fff" }}>
                <option value="">Customer (optional)…</option>
                {farmerOpts.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <input value={invEmail} onChange={e => setInvEmail(e.target.value)} placeholder="email it to…" type="email"
                style={{ flex:"1 1 180px", minWidth:0, fontSize:12, padding:"6px 8px", borderRadius:6, border:"1px solid #cdd9cd" }} />
              <button disabled={!invEmail.trim() || invBusy} onClick={() => makeInvite(true)}
                title="Email this link to the grower"
                style={{ border:"none", borderRadius:6, padding:"6px 12px", fontSize:11.5, fontWeight:800, cursor: invEmail.trim() && !invBusy ? "pointer" : "default", background: invEmail.trim() && !invBusy ? "#2d5a10" : "#dfe7df", color: invEmail.trim() && !invBusy ? "#fff" : "#677167", whiteSpace:"nowrap" }}>
                {invBusy ? "Sending…" : "✉ Email it"}
              </button>
            </div>
            {invOut && (
              <div style={{ background:"#fff", border:"1px solid #cfe3c6", borderRadius:8, padding:"8px 10px", fontSize:11.5, color:"#2d5a10", fontWeight:700 }}>
                {invOut.emailed && invOut.emailed.sent
                  ? `✓ Sent to ${invOut.emailed.to}${invOut.farmer ? ` for ${invOut.farmer}` : ""} — they appear below within 15 minutes of finishing.`
                  : invOut.emailed && invOut.emailed.error
                    ? <span style={{ color:"#b13a24" }}>Email didn't send: {invOut.emailed.error} — copy the link above and send it yourself.</span>
                    : "Link ready — copy it above."}
              </div>
            )}
            {data.invites && data.invites.length > 0 && (
              <div style={{ marginTop:9, borderTop:"1px solid #cfe3c6", paddingTop:8 }}>
                <div style={{ fontSize:10.5, letterSpacing:"0.05em", color:"#60735f", fontWeight:800, marginBottom:5 }}>SENT · NOT YET ACCEPTED</div>
                {data.invites.map(inv => (
                  <div key={inv.id} style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", fontSize:11.5, padding:"3px 0" }}>
                    <span style={{ fontWeight:700, color:"#2d4a2d" }}>{inv.farmer_name || inv.farmer_id}</span>
                    <span style={{ color:"#60735f" }}>{inv.email || "link only"} · sent {new Date(inv.created_at).toLocaleDateString()}</span>
                    <span style={{ marginLeft:"auto", display:"inline-flex", gap:6 }}>
                      <button onClick={() => copy(inv.url, inv.id)} style={{ border:"1px solid #cdd9cd", borderRadius:6, background:"#fff", padding:"3px 9px", fontSize:11, fontWeight:700, cursor:"pointer", color:"#2d4a2d" }}>{copied===inv.id ? "✓ Copied" : "Copy link"}</button>
                      <button onClick={() => cancelInvite(inv)} style={{ border:"1px solid #e3c6c6", borderRadius:6, background:"#fff", padding:"3px 9px", fontSize:11, fontWeight:700, cursor:"pointer", color:"#b13a24" }}>Cancel</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {data.connect_link && (
              <div style={{ marginTop:9, fontSize:11, color:"#60735f", lineHeight:1.5 }}>
                Growers can also reach the same screen themselves: Ops Center → Setup → Connections → search "clover".
                Either way the farm arrives unattributed and you match it below — <b>two policies on one Ops Center account is fine</b>, link the
                first and the second joins it.
              </div>
            )}
          </div>
        )}
        <div style={{ padding:"4px 8px 18px", maxHeight:"74vh", overflow:"auto" }}>
          {err && <div style={{ padding:16, color:"#c0392b", fontSize:13 }}>Couldn't load: {err}</div>}
          {!data && !err && <div style={{ padding:24, color:"#60735f", fontSize:13 }}><UiSpinner />Reading John Deere org list…</div>}
          {/* `orgs` is absent whenever the backend couldn't reach John Deere at all
              (no usable token, etc.) — it answers {ok:false, note}. Reading .map off
              that threw INSIDE the render, so the error boundary took down the whole
              app over one unavailable panel. Say what happened and stay up. */}
          {data && !Array.isArray(data.orgs) && (
            <div style={{ padding:20, fontSize:13, color:"#8a5a00", background:"#fff3d9", borderRadius:8, margin:"8px 10px" }}>
              Couldn't read the John Deere org list{data.note ? ` — ${data.note}` : ""}.
              {" "}Invites above still work; try <b>🔑 Re-authorize JD</b> if this persists.
            </div>
          )}
          {data && Array.isArray(data.orgs) && (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>{th("Operations Center Org")}{th("Status")}{th("Linked farmer")}{th("Last sync")}{th("")}</tr></thead>
              <tbody>
                {data.orgs.map((o) => (
                  <tr key={o.id} style={{ opacity: (o.linked_in_app || o.sharing || o.adopted) ? 1 : 0.5 }}>
                    {td(<span style={{ fontWeight:700 }}>{o.name}</span>)}
                    {/* A LINKED farm is connected — even partner-delegated ones that stay
                        member:false (Silsby/Triple-H/Corcoran sync fine but never flip the
                        "sharing" flag). Key the status off linked_in_app, not the member flag,
                        so an actively-syncing farm never reads "Not sharing". */}
                    {td(o.linked_in_app
                      ? (o.status==="error"
                          ? <span title={o.sync_error||""}>{pill("#fde8e4","#b13a24", /FIELD_ANALYZER|fieldOperations/i.test(o.sync_error||"") ? "Connected · needs agronomic (Work) access" : "Connected · needs field access")}</span>
                          : pill("#e5f3df","#2d6a2d","✓ Connected"))
                      : (o.adopted
                          /* We hold a token for it — it IS connected, it just has no
                             customer yet. Reading that as "Not sharing" is how Red Arrow
                             looked broken when nothing was wrong with it. */
                          ? pill("#fff3d9","#8a5a00","Connected · needs a customer")
                          : o.sharing
                            ? pill("#fff3d9","#8a5a00","Sharing · not linked")
                            : pill("#eef2ee","#60735f","Not sharing")))}
                    {td(o.farmer_name || "—")}
                    {td(o.last_sync_at ? new Date(o.last_sync_at).toLocaleDateString() : "—", { color:"#60735f", fontSize:11.5 })}
                    {/* The Link picker used to require o.sharing — JD's member flag —
                        so a partner-delegated org that we can READ but that reports
                        member:false had no control at all. Red Arrow Dairy sat adopted,
                        readable, tokened and unlinkable. Offer Link for anything we can
                        reach that has no customer yet. */}
                    {td((o.sharing || o.adopted || o.readable === true) && !o.linked_in_app ? (
                      <span style={{ display:"inline-flex", gap:6, alignItems:"center" }}>
                        <select value={pick[o.id] || ""} onChange={e => setPick(p => ({ ...p, [o.id]: e.target.value }))}
                          style={{ fontSize:11.5, padding:"4px 6px", borderRadius:6, border:"1px solid #cdd9cd", maxWidth:170 }}>
                          <option value="">Link to farmer…</option>
                          {farmerOpts.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                        <button disabled={!pick[o.id] || busy===o.id} onClick={() => link(o)}
                          style={{ border:"none", borderRadius:6, padding:"5px 10px", fontSize:11.5, fontWeight:700, cursor: pick[o.id] ? "pointer" : "default", background: pick[o.id] ? "#2d4a2d" : "#dfe7df", color: pick[o.id] ? "#fff" : "#677167" }}>
                          {busy===o.id ? "…" : "Link"}
                        </button>
                      </span>
                    ) : (o.linked_in_app ? (
                      <span style={{ display:"inline-flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                        {o.status==="error"
                          ? <span title={o.sync_error||""} style={{ fontSize:11, color:"#b13a24" }}>grower must share fields in Ops Center</span>
                          : <span style={{ fontSize:11, color:"#60735f" }}>synced daily</span>}
                        {/* A farm that has never synced needs three things run in order, and
                            getting one of them wrong is why "connected" and "usable" kept
                            being different states. One button, correct order, every time. */}
                        {/* NOT gated on status. A row that has never synced AND is in
                            error is the exact case that most needs the button — Panek
                            sat there with no control at all, because "Set up" required
                            status!=error and "Sync now" required a previous sync. The
                            sync itself force-refreshes and retries on a 403, so this is
                            also the fix for a grant that landed after the last probe. */}
                        {!o.last_sync_at && (
                          <button disabled={!!setup} onClick={() => setUpFarm(o)}
                            title="Pull this farm's fields and operations, build its yield maps, and attribute the acres — everything needed before the Field Data tab shows anything. Leave this open until it finishes."
                            style={{ border:"none", borderRadius:6, background: setup && setup.orgId===o.id ? "#eef2ee" : "#2d5a10", padding:"5px 11px", fontSize:11, fontWeight:800, cursor: setup ? "default" : "pointer", color: setup && setup.orgId===o.id ? "#2d5a10" : "#fff", whiteSpace:"nowrap", opacity: setup && setup.orgId!==o.id ? 0.4 : 1 }}>
                            {setup && setup.orgId===o.id ? setup.step : "▶ Set up this farm"}
                          </button>
                        )}
                        {/* Already set up: one button. The rest are repairs. Also shown
                            on an errored row so there is always something to press. */}
                        {(!!o.last_sync_at || o.status === "error") && (
                          <button disabled={syncing===o.id} onClick={() => syncNow(o)}
                            title="Re-run the John Deere sync for this farmer now"
                            style={{ border:"1px solid #cdd9cd", borderRadius:6, background: syncing===o.id ? "#eef2ee" : "#f6faf6", padding:"4px 9px", fontSize:11, fontWeight:700, cursor: syncing===o.id ? "default" : "pointer", color:"#2d4a2d", whiteSpace:"nowrap" }}>
                            {syncing===o.id ? "Syncing…" : "↻ Sync now"}
                          </button>
                        )}
                        {adv && <button disabled={repulling===o.id} onClick={() => repull(o)}
                          title="Full re-pull — picks up edits the grower made in Ops Center (e.g. a crop relabel) that a normal sync skips"
                          style={{ border:"1px solid #cdd9cd", borderRadius:6, background: repulling===o.id ? "#eef2ee" : "#f6faf6", padding:"4px 9px", fontSize:11, fontWeight:700, cursor: repulling===o.id ? "default" : "pointer", color:"#2d4a2d", whiteSpace:"nowrap" }}>
                          {repulling===o.id ? "Re-pulling…" : "⤓ Re-pull edits"}
                        </button>}
                        {adv && <button disabled={!!mapBuild} onClick={() => buildMaps(o)}
                          title="Force-build this farm's yield maps now (Machine vs FSA / By area need them). A big new farm otherwise takes days on the background cron. Leave open until it finishes, then Recompute maps."
                          style={{ border:"1px solid #cdd9cd", borderRadius:6, background: mapBuild && mapBuild.orgId===o.id ? "#eef2ee" : "#f6faf6", padding:"4px 9px", fontSize:11, fontWeight:700, cursor: mapBuild ? "default" : "pointer", color:"#2d4a2d", whiteSpace:"nowrap", opacity: (mapBuild && mapBuild.orgId!==o.id) ? 0.5 : 1 }}>
                          {mapBuild && mapBuild.orgId===o.id ? `Building… ${mapBuild.cached}` : "🗺 Build maps"}
                        </button>}
                        {adv && <button disabled={!!recompFarm} onClick={() => recompFarmer(o)}
                          title="Re-split this farm's stored yield maps by CLU ownership (footprint acres, physical county) — scoped to one farm, runs in seconds"
                          style={{ border:"1px solid #cdd9cd", borderRadius:6, background: recompFarm===o.id ? "#eef2ee" : "#f6faf6", padding:"4px 9px", fontSize:11, fontWeight:700, cursor: recompFarm ? "default" : "pointer", color:"#2d4a2d", whiteSpace:"nowrap", opacity: (recompFarm && recompFarm!==o.id) ? 0.5 : 1 }}>
                          {recompFarm===o.id ? "Recomputing…" : "↻ Recompute maps"}
                        </button>}
                      </span>
                    ) : "")) }
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── WHAT UNIT IS A HARVEST WATCH ROW IN? (2026-09-25) ───────────────────────
//
// Both Harvest Watch panels typed "bu/ac" beside the yield and the guarantee and
// "bu" beside the production and the shortfall. That is right for every crop in
// the book except one, and the exception is a SIXTY-fold error: RMA writes dry
// beans in POUNDS per acre, the whole claim-watch engine carries them in pounds to
// match, and Sullivan Farms' 1,881 lb/ac read on screen as **1,881 bu/ac** against
// a guarantee of "1,021 bu/ac" — on the panel headed LIKELY CLAIM.
//
// `?fn=jd-claimwatch` now states the unit per row (derived from the same
// `INSURED_IN_LB` that decided how the yield was stored, so a label cannot drift
// from its number). These are the two readers, and they are one rule rather than
// two copies — the repo's own record is that a rule written twice drifts twice.
//
// The fallback is bushels because that is what an older payload without the field
// MEANT; it is not a guess about the crop.
const watchUnit = (a) => (a && a.yield_unit) || "bu/ac";
// ...and the same unit for a TOTAL, which carries no "per acre": lb/ac -> lb.
const watchTotalUnit = (a) => (watchUnit(a) === "lb/ac" ? "lb" : "bu");

// `portal` = the grower is reading their own records. Every endpoint under this
// component is already caller-scoped (jd-fieldmap / jd-opsgrids / jd-fielddata /
// jd-claimwatch all go through gateScoped), so what changes in portal mode isn't
// access — it's which of these questions are the agency's rather than the farm's:
// "Machine vs FSA" is an acreage-reconciliation audit and "Harvest Watch" is a
// claim read. Both come off; By field / By area / Maps stay.
// ── WHICH PASS OPENS FOR A FIELD ────────────────────────────────────────────
// One rule for every way into a field's map: a row tapped after the maps loaded, a row
// tapped BEFORE they loaded (the phone's cold path, which used to fall back to the
// farm's first grid — another field's map under this one's name), a field opened from
// Field Mapper, an outline tapped on the map, and the list under it. The row's own
// crop-year first; the harvest map over the planting map; the newest first.
function jdPickFieldPass(mapGrids, jdFieldId, crop, year) {
  const mine = (mapGrids || []).filter(g => String(g.field_id) === String(jdFieldId));
  const newest = (a, b) => (b.year || 0) - (a.year || 0) || String(b.date || "").localeCompare(String(a.date || ""));
  const rank = (g) => (g.metric === "yield" ? 0 : g.metric === "seed_rate" ? 1 : 2);
  const inRow = mine.filter(g => (!crop || g.crop === crop) && (!year || String(g.year) === String(year)));
  const pool = inRow.length ? inRow : mine;
  const hit = [...pool].sort((a, b) => rank(a) - rank(b) || newest(a, b))[0];
  return { hit, inRow };
}
// ── A MAP'S YEAR IS ITS CROP YEAR ───────────────────────────────────────────
// The server stamps a grid with its CALENDAR year, while By field and By area use the
// crop year (api/_jd.js rmaCropYear: wheat or triticale drilled September to November
// is next year's crop). So the 2027 chip — Lawnel 2's wheat and triticale drilled this
// October — showed fields and no maps, and the 2026 chip hid the drill map of the wheat
// it was showing under a planting from the autumn before. Same rule, same window.
function jdGridCropYear(g) {
  if (!g || g.year == null) return g ? g.year : null;
  if (g.metric === "yield" || !/WHEAT|TRITICALE/i.test(String(g.crop || ""))) return g.year;
  const mo = Number(String(g.date || "").slice(5, 7));
  return (mo >= 9 && mo <= 11) ? Number(g.year) + 1 : g.year;
}
// The word a production figure is counted in, from the figure's OWN unit. Deriving it
// from the crop name printed "bu" beside alfalfa and triticale tonnage on By area.
const jdProdWord = (u) => (!u || u === "unit not recorded" ? "" : u === "ton/ac" ? "wet ton" : String(u).replace(/\/ac$/, ""));
// The unit as a farmer reads it beside a yield: a chopper's wet tons say so.
const jdShowUnit = (u) => (u === "ton/ac" ? "wet ton/ac" : u);
// The server stores silage as wet tons standardised to 35% dry matter (65% moisture) and
// tags it "ton/ac @35% DM". Farmers read it as wet tons per acre: "We always measure silage
// yields in wet tons per acre" (Kevin, 2026-11-01). Display only; the stored unit is unchanged.
const silageUnitLabel = (u) => (u === "ton/ac @35% DM" ? "wet ton/ac" : u);
// A By-field row that recorded NOTHING: no planted acres, no harvested acres, no yield,
// and at most a zero the monitor wrote with nothing behind it. Lawnel 2's field 19
// carries one — a CORN harvest on 13 July 2026, 0 ac, 0 bu, 0% moisture, two weeks
// after its alfalfa was cut — which read as a corn harvest on the Corn chip.
const jdFieldRowEmpty = (r) => !!r && !(Number(r.plant_ac) > 0) && !(Number(r.harv_ac) > 0) && r.yield == null
  && (!r.yield_refused || !(Number(r.yield_refused.recorded) > 0));
// Corn planted in May is not combined for grain before August. A CORN harvest dated May
// to July is a forage cut the monitor left set to Corn, so it never counts as corn grain.
const jdCornHarvestMisfiled = (r) => !!r && String(r.crop || "").toUpperCase() === "CORN" && !!r.harv_date
  && (() => { const mo = Number(String(r.harv_date).slice(5, 7)); return mo >= 5 && mo <= 7; })();
function JdFieldDataView({ farmer, farmerId, onClose, embedded = false, portal = false, initialFieldId = null, initialFieldName = null, initialYear = null, initialCrop = null, backLabel = null, initialView = null, initialMetric = null }) {
  // ⚠ THE COMPONENT DECIDES, NOT ITS CALLERS. Kevin, 2026-10-31, previewing a farmer
  // (Amos Smith) on his phone: Field Mapper → tap a field → Machine data. Two of the
  // three routes in here never passed `portal` — the Field Mapper field popup and the
  // FarmerView "Field Data" button, and FarmerView IS the portal's My Coverage page —
  // so a farmer got the agency's Machine vs FSA and Harvest Watch tabs and Draft AR.
  // `portal` stays as an explicit override, but anyone who is not an admin by
  // EFFECTIVE identity (false in the portal and in preview-as) is treated as portal
  // here, so a caller that forgets the prop can no longer open the agency view.
  // tools/check-machine-data-portal.js holds this.
  const isPortal = portal || !isCurrentUserAdmin();
  // ⚠ THE MAPS TAB WAS A TWO-COLUMN DESKTOP LAYOUT ON A PHONE. The field list is a
  // FIXED 210px column, so at 390px the map and its detail panel share what is left
  // — about 180px — and the panel's own text ("H2 · LIVINGSTON", the PASS count, the
  // Harvest button) is clipped off the right edge while the scale control sits on
  // top of the map. Kevin, 2026-09-06, on his phone. Stacked below.
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // ── PHONE FIRST, MAP FIRST (Kevin, 2026-10-31: "rethink the machine data view";
  // mockup approved the same day). Opened from a field, it lands on THAT field's map.
  // Otherwise a phone opens on the field list (cards, not a nine-column table) and a
  // desktop on the map, with the table one tab away.
  // The Season home deep-links straight to the Maps tab, Harvest or Planting (initialView /
  // initialMetric); every other caller passes neither and keeps the behaviour below.
  const [view, setView] = useState(initialView || (initialFieldId ? "maps" : (isMobile ? "field" : "maps")));   // field | area | maps | compare | watch
  const [moreOpen, setMoreOpen] = useState(false);  // phone: the full filter bar, folded away
  const [cardCap, setCardCap] = useState(150);      // phone: field cards drawn before "Show more"
  const [metric, setMetric] = useState(initialMetric === "seed_rate" ? "seed_rate" : "yield");    // yield (harvest) | seed_rate (planting)
  const [groupBy, setGroupBy] = useState("county"); // county | unit | fsn | tract | field  (area/compare grouping)
  const [calOpen, setCalOpen] = useState(false);    // post-calibration entry panel
  const [calForm, setCalForm] = useState({ crop:"", year:"", bushels:"", source:"scale tickets" });
  const [calBusy, setCalBusy] = useState(false);
  const [calErr, setCalErr]   = useState(null);
  const [calNonce, setCalNonce] = useState(0);   // bump to re-pull the area feed after a save
  // Opens on the year and crop Field Mapper is showing, so the page you land on is the
  // one you were looking at. A year with no machine data falls back to the newest one.
  const [cropFilter, setCropFilter] = useState(initialCrop ? String(initialCrop).toUpperCase() : ""); // "" = all crops
  const [yearFilter, setYearFilter] = useState(initialYear != null ? String(initialYear) : ""); // "" = all years
  const [coverOpen, setCoverOpen] = useState(false); // phone: the cover-crop chips, folded away
  // Plant/harvest DATE window. Crop year is derived and lossy — a fall-seeded crop
  // spans two calendar years, and Ops Center labels it inconsistently between
  // growers. The operation date is the one fact that never moves, so this filters
  // on it directly: "what went in after the final planting date", "what did we
  // plant that first week of May", "everything drilled last autumn".
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo]     = useState("");
  // Field search, the one control Work Analyzer has that this page did not. Swede Farms
  // is 525 fields; a crop-and-year filter still leaves a table nobody can find a name in.
  const [fieldQ, setFieldQ] = useState("");
  const [sort, setSort] = useState({ k: "year", d: -1 });
  const [watch, setWatch] = useState(null);         // harvest-watch / claim-detection payload
  const [watchErr, setWatchErr] = useState(null);
  // 🗺 Maps browser (JD-Field-Analyzer-style): flip through this farm's field yield
  // maps. Reuses jd-opsgrids (all built grids, own + custom-attributed) + the same
  // d3.contours renderer as the main Field Mapper.
  const [mapGridsRaw, setMapGrids] = useState(null);   // [{op_id, field, field_id, crop, county, year, metric, bbox, values, nx, ny, cell, mean}]
  // ⚠ CALIBRATED ON READ, NOT AT LOAD. The load used to scale each grid by the factor in
  // `data`, so when the grids landed before the field data (the usual order on Maps) the
  // factor was null and the grids stayed raw for good, and pressing Apply on a new
  // calibration re-pulled the tables and left every map as it was. This derives the
  // calibrated grids from the raw ones whenever either changes. Each scaled copy is kept
  // per raw grid and factor, so the smoothed surface a copy caches is not rebuilt on
  // every render.
  const calCacheRef = useRef(new WeakMap());
  const calMap = data && data.calibration ? data.calibration : null;
  const mapGrids = useMemo(() => {
    if (!mapGridsRaw) return mapGridsRaw;
    return mapGridsRaw.map(x => {
      const c = x.metric === "yield" && calMap ? calMap[`${String(x.crop || "").toUpperCase()}|${Number(x.year)}`] : null;
      const f = c && c.factor > 0 ? c.factor : null;
      if (!f) return x;
      const was = calCacheRef.current.get(x);
      if (was && was._cal === f) return was;
      const y = { ...x, _filled: null, _fillSmooth: null, _fillSorted: null, _cal: f,
        values: Array.isArray(x.values) ? x.values.map(v => v == null ? v : v * f) : x.values,
        mean: x.mean != null ? Math.round(x.mean * f * 10) / 10 : x.mean,
        bu_mean: x.bu_mean != null ? Math.round(x.bu_mean * f * 10) / 10 : x.bu_mean };
      calCacheRef.current.set(x, y);
      return y;
    });
  }, [mapGridsRaw, calMap]);
  const [mapSel, setMapSel] = useState(null);       // selected op_id
  // The field asked for before the grids arrived — opened from Field Mapper, or a card
  // tapped on a phone while the maps were still loading. See openFieldOnMap.
  const openReqRef = useRef(initialFieldId ? { id: initialFieldId, crop: initialCrop ? String(initialCrop).toUpperCase() : null, year: initialYear != null ? String(initialYear) : null } : null);
  const [mapField, setMapField] = useState(initialFieldId);   // selected field key (groups ops by field)
  // Jump from a flagged field straight to its map. A review flag you cannot locate is a
  // to-do that never gets done, so the alert names its fields and each one opens here.
  // Clears the county and search filters on the way — landing on the Maps tab with the
  // field filtered out would look like the link was broken.
  // ── CLICK A FIELD, GET ITS MAP (Kevin, 2026-09-24) ─────────────────────────
  // *"In field machine data section of field mapper, if i click a field, i need to be
  // able to get to machine data maps for that field somehow."*
  //
  // The By-field table is the list of every field the machine worked, and the only route
  // from a row to that field's picture was: read the name, switch to Maps, find the name
  // again in a dropdown of every field on the farm. This is the same jump the unmatched-
  // acres banner has had since August, opened up to the table it belongs on.
  //
  // `openFieldName` is carried so the Maps view can NAME a field it cannot draw. Without
  // it, a field with no grid lands on a Maps tab showing nothing, which reads as a broken
  // link rather than as the fact it is — see the no-map branch there.
  const openFieldOnMap = (jdFieldId, crop, year, fieldName) => {
    setMapCounty(""); setMapQ(""); setMapMode("field");
    if (crop) setCropFilter(crop);
    if (year) setYearFilter(String(year));
    setMapField(jdFieldId);
    setOpenFieldName(fieldName || null);
    // Prefer the harvest map — it is what an agent is nearly always after — but a field
    // that was only PLANTED has a seeding map and no yield one, and landing on it with
    // nothing selected is the same dead end by another route.
    //
    // ⚠ THE ROW'S OWN CROP-YEAR, NEWEST FIRST. This took the FIRST yield grid of any
    // year, which on Lawnel 2's field 1702 was a 2017 alfalfa harvest — while the chips
    // above were set to the row's Corn silage 2026. The filter hid that pass, so its
    // pixels were never fetched, nothing drew and the map never zoomed in; the card
    // said "harvested 2017-08-11" under a 2026 planting pass (Kevin, 2026-10-31).
    // ⚠ AND WHEN THE MAPS HAVE NOT LOADED YET, THE ASK IS KEPT. On a phone Machine data
    // opens on By field, so the first tap usually lands before the grids do; the load
    // then picked the farm's FIRST grid and painted another field under this one's
    // name. The request waits in a ref and the load runs this same pick on it.
    if (!mapGrids) openReqRef.current = { id: jdFieldId, crop: crop || null, year: year || null };
    const { hit, inRow } = jdPickFieldPass(mapGrids, jdFieldId, crop, year);
    if (hit) {
      setMapSel(hit.op_id);
      // A field with nothing under the row's crop-year opens on its newest map
      // instead; move the chips to that map so the filter never hides what is drawn.
      if (!inRow.length) { setCropFilter(hit.crop || ""); setYearFilter(hit.year ? String(hit.year) : ""); }
    }
    setView("maps");
  };
  const [openFieldName, setOpenFieldName] = useState(initialFieldName || (initialFieldId ? "this field" : null));
  const [mapQ, setMapQ] = useState("");             // field-name search
  const [mapCounty, setMapCounty] = useState("");   // county filter
  const [mapScale, setMapScale] = useState(null);   // { lo, hi } manual color-scale override
  const [mapErr, setMapErr] = useState(null);
  const mapDivRef = useRef(null), mapTopRef = useRef(null), mapObjRef = useRef(null), mapLayerRef = useRef(null), mapFitRef = useRef(null);
  // "One field" paints the selected map; "Compare" paints EVERY field of one
  // crop-year on a single shared scale, which is the only way to see that the
  // north farm ran 40 bu behind the home place. Deliberately no operation
  // picker: the operation is the farm already chosen on this page, so there is
  // no roster of other growers to render — that roster is what leaked on
  // 2026-08-04, and the safest version of it is the one that doesn't exist.
  const [mapMode, setMapMode] = useState("field");  // field | compare
  const [mapCombo, setMapCombo] = useState(null);   // "metric|crop|year" drawn in compare mode
  const [mapBounds, setMapBounds] = useState(null); // this farm's JD field boundaries (GeoJSON)
  const mapBoundsRef = useRef(null), mapCmpRef = useRef([]);
  // Value under the cursor, read off the painted surface. A per-field tooltip
  // can't answer this — it's ONE average for the whole polygon, so it sat
  // unchanged while the mouse crossed a map full of variation.
  const [mapHover, setMapHover] = useState(null);   // { v, unit, field, mean, x, y }
  const mapMoveRef = useRef(null);
  // One filtered set, shared by the field list, the boundary highlight and the
  // compare pass — so what you see drawn is what the filters say, everywhere.
  const mapGridsF = useMemo(() => {
    const q = mapQ.trim().toLowerCase();
    return (mapGrids || []).filter(g =>
      (!cropFilter || g.crop === cropFilter) &&
      (!yearFilter || String(g.year) === String(yearFilter)) &&
      (!mapCounty || String(g.county || "") === mapCounty) &&
      (!q || String(g.field || "").toLowerCase().includes(q) || String(g.ftf_label || "").toLowerCase().includes(q)));
  }, [mapGrids, cropFilter, yearFilter, mapCounty, mapQ]);
  // Compare needs ONE metric, ONE crop, ONE year — AND ONE UNIT. Corn at 195 and
  // soybeans at 53 on a shared ramp paints every bean field as a failure, and
  // bu/ac against seeds/ac isn't even the same quantity — so the combos are
  // enumerated and one is drawn, never blended.
  //
  // THE UNIT IS PART OF THE IDENTITY, AND LEAVING IT OUT WAS NOT THEORETICAL.
  // The metric does NOT determine the unit: seed_rate is seeds/ac on 9,848 grids
  // and lb/ac on 1,917, and one farm runs BOTH in the same season — Austic Farm
  // Partners' SOYBEANS 2024 is 38 grids at 44.9–77.5 lb/ac beside 95 grids at
  // 92,908–179,523 seeds/ac. Keyed on metric|crop|year those were ONE combo: a
  // shared ramp spanning 45 to 179,523 paints all 38 pound-rate fields flat at
  // the bottom — i.e. as failures — the "operation avg" is an acre-weighted blend
  // of pounds and seeds, and the unit printed beside it came from whichever grid
  // sorted first. Measured 2026-08-22: 34 such combos across 11 farms, 1,624
  // grids. Same unit-of-measure conflation CLAUDE.md forbids everywhere else, on
  // the one screen where a grower reads the number and ranks their own ground.
  //
  // A grid whose unit was never recorded gets its OWN bucket rather than joining
  // a known one. A missing label is not evidence that it matches; those are older
  // grids written before the column was populated, and folding them in is the
  // same assumption running the other way.
  const mapCombos = useMemo(() => {
    const m = {};
    for (const g of mapGridsF) {
      // COVERAGE IS A ONE-FIELD PASS, NOT A COMPARISON. Its cells carry no rate —
      // only that the planter was there — so its mean is ~1 and ranking fields on
      // it would compare nothing while printing a number that looks like data.
      // It stays selectable per field (that is the planting map for a farm whose
      // export had no rate column) and stays out of Compare entirely.
      if (g.metric === "coverage") continue;
      const k = jdComboKey(g);
      (m[k] = m[k] || { key: k, metric: g.metric, crop: g.crop || "?", year: g.year || null,
                        unit: g.unit || null, n: 0 }).n++;
    }
    // YIELD FIRST, newest first — and that ordering picks the default, which is
    // the whole point. Sorting by field count put a PLANTING map at the top
    // (planting covers every field the day it's done; harvest grids build in over
    // weeks), so compare opened on Sullivan's WHEAT 2025 seed rate: 98% of the
    // operation drilled between 128.8 and 130.1 lb/ac. A planter runs a commanded
    // rate, so every field drew as one flat block — correct rendering of a map
    // that has no sub-field variation in it, and it read as a broken compare view.
    const rank = (x) => (x.metric === "seed_rate" ? 1 : 0);
    return Object.values(m).sort((a, b) =>
      rank(a) - rank(b) || (b.year || 0) - (a.year || 0) || b.n - a.n || String(a.crop).localeCompare(String(b.crop)));
  }, [mapGridsF]);
  // Cheap on purpose: which fields are in the combo, and the operation average.
  // The colour scale is NOT computed here — it needs the smoothed surface, which
  // costs a full pass over every grid, and that belongs in the draw effect (after
  // paint) rather than in a render-blocking memo. See mapAuto below.
  const mapCmp = useMemo(() => {
    if (mapMode !== "compare") return null;
    const c = mapCombos.find(x => x.key === mapCombo) || mapCombos[0];
    if (!c) return null;
    const grids = mapGridsF.filter(g => jdComboKey(g) === c.key);
    // Acre-weighted average of the field means — the operation's real figure.
    // Averaging the field means flat would let a 12-acre corner count the same as
    // a 300-acre block; falls back to a flat mean when JD reported no area.
    let wsum = 0, w = 0, flat = 0, nf = 0, dsum = 0, dflat = 0, dn = 0;
    for (const g of grids) {
      if (g.mean == null) continue;
      nf++; flat += g.mean;
      const a = g.area != null ? Number(g.area) : 0;
      if (a > 0) { wsum += g.mean * a; w += a; }
      if (g.mean_dm != null) { dn++; dflat += g.mean_dm; if (a > 0) dsum += g.mean_dm * a; }
    }
    const mean = w > 0 ? +(wsum / w).toFixed(1) : (nf ? +(flat / nf).toFixed(1) : null);
    // Silage reads in wet tons; the chopper's dry matter rides along as a second figure,
    // and only when every field in the set carries one (Kevin, 2026-11-01).
    const meanDm = dn && dn === nf ? (w > 0 ? +(dsum / w).toFixed(1) : +(dflat / dn).toFixed(1)) : null;
    return { combo: c, grids, mean, meanDm };
  }, [mapMode, mapCombo, mapCombos, mapGridsF]);
  // The auto scale actually in force, published by the draw effect so the scale
  // box shows the same numbers the picture was painted with.
  const [mapAuto, setMapAuto] = useState(null);   // { lo, hi }
  useEffect(() => {   // keep the drawn combo valid as the filters move under it
    if (mapMode !== "compare" || !mapCombos.length) return;
    if (!mapCombo || !mapCombos.some(c => c.key === mapCombo)) setMapCombo(mapCombos[0].key);
  }, [mapMode, mapCombos]); // eslint-disable-line
  useEffect(() => {   // this farm's JD field boundaries, so the map shows the operation
    if (view !== "maps" || mapBounds) return;
    // ?farmer= is not a convenience here — it is the scope. The server intersects
    // it with the caller's grants, which is what keeps a portal session (and an
    // admin previewing one, who still holds an admin token) to this one farm.
    authedFetch(`/api/market?fn=jd-fieldmap&farmer=${farmerId || farmer.id}`)
      .then(r => r.json())
      .then(gj => setMapBounds(gj && gj.features ? gj : { type: "FeatureCollection", features: [] }))
      .catch(() => setMapBounds({ type: "FeatureCollection", features: [] }));
  }, [view]); // eslint-disable-line
  useEffect(() => {   // fetch the farm's yield grids the first time the Maps tab opens
    if (view !== "maps" || mapGrids) return;
    const fid = farmerId || farmer.id;
    // METADATA FIRST, PIXELS ON DEMAND. This used to ask for every grid's full `values`
    // array for the whole farm and both metrics. One grid averages ~0.9 MB, so as a
    // farm's built maps accumulate the request grows without bound — Panek Farms hit
    // 237 MB on seed_rate and a hard FUNCTION_INVOCATION_FAILED on yield, which is why
    // Maps sat on "Loading maps…" and Compare reported no built maps. The field list,
    // the labels, the combo dropdowns and the boundary highlight never needed pixels;
    // only the map actually being drawn does, and that is one crop-year at a time.
    // COVERAGE IS A THIRD PASS AND IT WAS NEVER ASKED FOR. A planting export that
    // carries no usable rate column still records where the planter DROVE, and those
    // build as metric='coverage' rather than as a fake rate grid of 1s (see the
    // 2026-08-13 note in CLAUDE.md). 1,033 of them exist across 12 farms — built,
    // stored, and unreachable from this screen, because only yield and seed_rate were
    // ever fetched. To the grower that reads as "we have no map of that planting".
    Promise.all([
      authedFetch(`/api/market?fn=jd-opsgrids&farmer=${fid}&metric=yield&novalues=1`).then(r => r.json()).catch(() => null),
      authedFetch(`/api/market?fn=jd-opsgrids&farmer=${fid}&metric=seed_rate&novalues=1`).then(r => r.json()).catch(() => null),
      authedFetch(`/api/market?fn=jd-opsgrids&farmer=${fid}&metric=coverage&novalues=1`).then(r => r.json()).catch(() => null),
    ]).then(([y, s, cv]) => {
      const g = [
        ...(((y && y.grids) || []).map(x => ({ ...x, metric: "yield" }))),
        ...(((s && s.grids) || []).map(x => ({ ...x, metric: "seed_rate" }))),
        ...(((cv && cv.grids) || []).map(x => ({ ...x, metric: "coverage" }))),
      // `values` no longer arrives here (novalues=1), so a grid qualifies on its BBOX —
      // gating on values would now discard every row and empty the whole tab.
      ].filter(x => x && x.bbox);
      // A FORAGE OR UNKNOWN CROP IS NEVER A BUSHEL MAP. The server already withholds the
      // stamped bu/ac on forage (BUG-061), but John Deere's "no crop picked" code
      // (UNSPECIFIED_CROP) still arrives as bu/ac — Lawnel 2's 21 hay cuttings at 259.8.
      // And each map takes its CROP year, the year By field and the chips use.
      for (const x of g) {
        if (x.metric === "yield" && x.unit && /^bu\s*\/\s*ac$/i.test(String(x.unit)) && !jdYieldUnitOrNull(x.crop, null)) x.unit = null;
        const cy = jdGridCropYear(x);
        if (cy !== x.year) { x.cal_year = x.year; x.year = cy; }
      }
      setMapGrids(g);
      // ⚠ THE FIELD THAT WAS ASKED FOR, NEVER THE FARM'S FIRST GRID. `g[0]` is whatever
      // the server sorted first — another field's map, painted under this one's name.
      const req = openReqRef.current;
      openReqRef.current = null;
      if (req) {
        const { hit, inRow } = jdPickFieldPass(g, req.id, req.crop, req.year);
        setMapField(req.id);
        if (hit) {
          setMapSel(hit.op_id);
          if (!inRow.length) { setCropFilter(hit.crop || ""); setYearFilter(hit.year ? String(hit.year) : ""); }
        }
      } else if (g.length) { setMapSel(v => v || g[0].op_id); setMapField(v => v || (g[0].field_id || g[0].op_id)); }
    }).catch(e => setMapErr(String((e && e.message) || e)));
  }, [view]); // eslint-disable-line
  // PIXELS FOR WHAT IS ON SCREEN. The metadata load above deliberately has no `values`,
  // so this pulls them for the crop-years actually being drawn and merges them in. Each
  // (metric, crop, year) is fetched at most once — `_px` records which are already in —
  // so panning and re-selecting never re-download a map.
  const [pxLoaded, setPxLoaded] = useState({});
  useEffect(() => {
    if (view !== "maps" || !mapGrids || !mapGrids.length) return;
    const fid = farmerId || farmer.id;
    // ONLY WHAT IS BEING DRAWN. Scoping this to "everything the filters expose" would
    // walk every combo over successive passes and re-download the whole farm a slice at
    // a time — the same 237 MB, just spread out. Compare paints exactly ONE
    // metric|crop|year; field mode paints ONE operation, whose combo is all it needs.
    //
    // THE FETCH KEY, NOT THE COMBO KEY — jdGridFetchKey, because the endpoint takes
    // no unit and one request answers both. Keying pxLoaded on the combo instead
    // would re-download an identical payload whenever a grower switched between the
    // seeds/ac and lb/ac views of one crop-year. These were two hand-written
    // literals in a THIRD shape (bare `g.crop`, no "?" fallback), which is exactly
    // how they came to disagree with the combo key the moment it gained the unit.
    // ⚠ FETCHED BY THE CALENDAR YEAR the server files a grid under. A wheat drill map
    // from October carries crop year 2027 here (jdGridCropYear) and calendar year 2026
    // on the server, which is the year the endpoint's date window has to ask for.
    const fetchKey = (g) => jdGridFetchKey(g.cal_year != null ? { ...g, year: g.cal_year } : g);
    const drawnGrids = mapMode === "compare"
      ? ((mapCmp && mapCmp.grids) || [])
      // THE SELECTED PASS FIRST, from the whole list — not only the filtered one.
      // Looking it up in mapGridsF alone fell back to mapGridsF[0] whenever a filter
      // hid the selected pass, so the map being drawn never got its pixels.
      : (() => { const g = mapGrids.find(x => x.op_id === mapSel) || mapGridsF[0]; return g ? [g] : []; })();
    const need = [...new Set(drawnGrids.filter(g => g && g.crop && g.year && !Array.isArray(g.values)).map(fetchKey))]
      .filter(k => !pxLoaded[k]);
    if (!need.length) return;
    setPxLoaded(p => { const n = { ...p }; for (const k of need) n[k] = 1; return n; });
    Promise.all(need.map(k => {
      const [metric, crop, year] = k.split("|");
      return authedFetch(`/api/market?fn=jd-opsgrids&farmer=${fid}&metric=${metric}&crop=${encodeURIComponent(crop)}&year=${year}`)
        .then(r => r.json()).then(j => ({ k, grids: (j && j.grids) || [] })).catch(() => ({ k, grids: [] }));
    })).then(res => {
      const byOp = {};
      for (const r of res) for (const g of r.grids) if (Array.isArray(g.values)) byOp[g.op_id] = g.values;
      if (!Object.keys(byOp).length) return;
      setMapGrids(prev => (prev || []).map(x => {
        const v = byOp[x.op_id];
        if (!v || Array.isArray(x.values)) return x;
        return { ...x, values: v };
      }));
    });
  }, [view, mapGrids, mapGridsF, pxLoaded, mapMode, mapCombo, mapCmp, mapSel]); // eslint-disable-line
  useEffect(() => {   // init/destroy the Leaflet map + render boundaries and the yield map(s)
    if (view !== "maps") {
      if (mapObjRef.current) { try { mapObjRef.current.remove(); } catch (e) {} mapObjRef.current = null; mapLayerRef.current = null; mapBoundsRef.current = null; mapCmpRef.current = []; mapMoveRef.current = null; }
      return;
    }
    let cancelled = false;
    Promise.all([loadLeaflet(), d3Promise]).then(([L]) => {
      if (cancelled || !mapDivRef.current) return;
      let map = mapObjRef.current;
      // ⚠⚠ A LEAFLET MAP OUTLIVES ITS OWN CONTAINER, AND REUSING IT DRAWS NOTHING.
      //
      // `mapObjRef` is only cleared when `view` leaves "maps" — but the map DIV
      // unmounts for a second reason this cleanup never sees: the "No machine map
      // for this field" empty state replaces the whole list-and-map area while the
      // view is still "maps". Pick a field that has no map for the selected crop,
      // then pick one that does, and the div remounts as a FRESH element while this
      // ref still points at a Leaflet instance bound to the element that was thrown
      // away. `if (!map)` is then false, `L.map()` is never called again, and every
      // tile, pane and overlay stays attached to a node that is no longer in the
      // document.
      //
      // Measured on CY FARMS LLC, 2026-09-29, through the shipped page:
      //   straight to Maps ............ .leaflet-container true  · 48 tiles · 7 panes
      //   via the WHEAT 2026 empty state .............. false · 0 tiles · 0 panes
      //
      // It reads as a dead map rather than as an error, and it IS silent: nothing
      // throws, so `check-screens` called the page clean. The header, the colour
      // scale and the field card all render from React state and are unaffected,
      // which is why the page looks alive with a white hole in the middle of it.
      if (map && map.getContainer() !== mapDivRef.current) {
        try { map.remove(); } catch (e) {}
        map = null;
        mapObjRef.current = null; mapLayerRef.current = null; mapBoundsRef.current = null;
        mapCmpRef.current = []; mapMoveRef.current = null;
        // ⚠ AND THE FIT TRACKER, which the view-change cleanup does NOT clear — it
        // does not have to, because a map that survives a view change keeps the
        // fit that goes with it. A map REPLACED under the same selection is the
        // other case: `mapFitRef` still equals this field's key, both fit guards
        // below short-circuit, and the new map sits at the default [43,-77.5]
        // zoom 8 — tiles and overlay present, pointed at nothing. That is the
        // same defect wearing a less obvious face, so it is cleared here rather
        // than found later as "the map opens zoomed out".
        mapFitRef.current = null;
      }
      if (!map) {
        map = L.map(mapDivRef.current, { zoomControl: true, attributionControl: false }).setView([43, -77.5], 8);
        L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 20 }).addTo(map);
        // Transparent reference overlays (free Esri, same terms as the imagery):
        // county/town boundaries + place names, and a separate roads-with-names layer.
        L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}", { maxZoom: 20, opacity: 0.9 }).addTo(map);
        L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", { maxZoom: 20, opacity: 0.9 }).addTo(map);
        map.on("mouseout", () => setMapHover(null));
        mapObjRef.current = map;
      }
      // Sized BEFORE anything is fitted. Deferring this was why the map opened at
      // region zoom with the field somewhere off screen: fitBounds was measuring a
      // container Leaflet still thought was 0×0.
      try { map.invalidateSize(); } catch (e) {}
      setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 80);
      if (mapLayerRef.current) { try { map.removeLayer(mapLayerRef.current); } catch (e) {} mapLayerRef.current = null; }
      if (mapBoundsRef.current) { try { map.removeLayer(mapBoundsRef.current); } catch (e) {} mapBoundsRef.current = null; }
      mapCmpRef.current.forEach(l => { try { map.removeLayer(l); } catch (e) {} }); mapCmpRef.current = [];

      // One field's grid → a d3.contours band layer, same renderer as the Field
      // Mapper so a map reads identically on both pages.
      const contour = (g, lo, hi, colorFn) => {
        const d3 = window.d3;
        if (!d3 || !d3.contours || !g || !g.values || !g.bbox) return null;
        const nx = g.nx, ny = g.ny, xmin = g.bbox[0], ymin = g.bbox[1], xmax = g.bbox[2], ymax = g.bbox[3];
        const filled = jdFilledValues(g);   // cached — compare redraws 27 of these
        if (!filled) return null;
        const dx = (xmax - xmin) / nx, dy = (ymax - ymin) / ny, sentinel = lo - (hi - lo) - 1;
        const dense = new Array(nx * ny);
        for (let i = 0; i < nx * ny; i++) { const v = filled[i]; dense[i] = (v == null ? sentinel : v); }
        const thr = d3.range(15).map(k => lo + (hi - lo) * (k / 14));
        const bands = d3.contours().size([nx, ny]).thresholds(thr).smooth(true)(dense);
        const feats = bands.filter(c => c.value >= lo && c.coordinates.length).map(c => ({
          type: "Feature", properties: { v: c.value },
          geometry: { type: "MultiPolygon", coordinates: c.coordinates.map(poly => poly.map(ring => ring.map(([x, y]) => [xmin + x * dx, ymin + y * dy]))) },
        }));
        if (!feats.length) return null;
        return L.geoJSON({ type: "FeatureCollection", features: feats }, { interactive: false, style: (f) => ({ stroke: false, fill: true, fillColor: colorFn(f.properties.v, lo, hi), fillOpacity: 0.92 }) });
      };

      // Which grids are actually being painted this pass — both branches below
      // draw from this, and the hover readout samples it.
      const curG = (mapGrids || []).find(x => x.op_id === mapSel);
      const drawn = mapMode === "compare" ? ((mapCmp && mapCmp.grids) || []) : (curG ? [curG] : []);
      const drawnIds = new Set(drawn.map(g => g.field_id).filter(Boolean));

      // ── THE VALUE UNDER THE CURSOR ─────────────────────────────────────────
      // Sampled off the SAME filled surface that was painted, so the number
      // always matches the colour under the pointer.
      //
      // This readout was DESCRIBED and never shipped. Commit bfda348 added the
      // state, the teardown, the mouseout clear and the tooltip markup — and no
      // handler anywhere ever SET mapHover, so `setMapHover` appeared exactly
      // twice in the file: the declaration and the clear. The same commit
      // dropped the per-field boundary tooltip for any field whose map is drawn,
      // on the stated grounds that "the live readout already names it". So
      // hovering a painted field went from one average to NOTHING, which is what
      // reads as the feature having stopped working.
      const sampleAt = (ll) => {
        for (const g of drawn) {
          const b = g.bbox;
          if (!b || !g.nx || !g.ny) continue;
          if (ll.lng < b[0] || ll.lng > b[2] || ll.lat < b[1] || ll.lat > b[3]) continue;
          const vals = jdFilledValues(g);   // cached — the surface contour() drew
          if (!vals) continue;
          const ix = Math.min(g.nx - 1, Math.max(0, Math.floor((ll.lng - b[0]) / (b[2] - b[0]) * g.nx)));
          const iy = Math.min(g.ny - 1, Math.max(0, Math.floor((ll.lat - b[1]) / (b[3] - b[1]) * g.ny)));
          const v = vals[iy * g.nx + ix];
          // A GAP IN THE SWATH KEEPS LOOKING rather than reporting 0. Grid
          // bboxes are rectangles and overlap where two fields sit beside each
          // other, so the first bbox a point falls in is not always the field
          // that was harvested there — and a 0 bu/ac readout is a yield claim
          // about ground the machine never crossed.
          if (v == null) continue;
          return { g, v };
        }
        return null;
      };
      const onMove = (e) => {
        const hit = sampleAt(e.latlng);
        if (!hit) { setMapHover(h => (h ? null : h)); return; }
        const dec = Math.abs(hit.v) >= 1000 ? 0 : 1;
        setMapHover({
          v: Number(hit.v.toFixed(dec)),
          // THE GRID'S OWN UNIT, never one derived from the metric — seed_rate is
          // seeds/ac on 9,848 grids and lb/ac on 1,917, and 8 yield grids are
          // ton/ac corn silage.
          unit: hit.g.unit ? ` ${jdShowUnit(hit.g.unit)}` : "",
          field: jdFieldLabel(hit.g),
          mean: hit.g.mean != null ? hit.g.mean : null,
          x: e.containerPoint.x, y: e.containerPoint.y,
        });
      };
      // SWAPPED, never stacked. This effect re-runs on every redraw, so an
      // added-each-pass handler would leave the map sampling grids it is no
      // longer painting.
      // A PHONE HAS NO HOVER. The same sampler answers a TAP, so "what did this spot
      // do" works under a thumb too (it was mousemove-only, Kevin 2026-10-31).
      if (mapMoveRef.current) { try { map.off("mousemove", mapMoveRef.current); map.off("click", mapMoveRef.current); } catch (err) {} }
      map.on("mousemove", onMove);
      map.on("click", onMove);
      mapMoveRef.current = onMove;

      // Every JD field boundary this farm has. Fields the filters keep draw solid;
      // the rest stay as faint outlines rather than disappearing, because an empty
      // map can't tell you whether the filter is wrong or the data is missing.
      if (mapBounds && mapBounds.features && mapBounds.features.length) {
        const shown = new Set(mapGridsF.map(g => g.field_id).filter(Boolean));
        const selIds = new Set((mapGrids || []).filter(g => (g.field_id || g.op_id) === mapField).map(g => g.field_id).filter(Boolean));
        const meanBy = {};
        for (const g of ((mapCmp && mapCmp.grids) || [])) if (g.field_id != null && g.mean != null) meanBy[g.field_id] = g.mean;
        // The COMBO's unit, never grids[0]'s. Every grid in a combo shares it by
        // construction now, but reading it off the first row is exactly how the
        // label came to disagree with the number it sat beside.
        const unit = (mapCmp && mapCmp.combo && mapCmp.combo.unit) ? ` ${jdShowUnit(mapCmp.combo.unit)}` : "";
        mapBoundsRef.current = L.geoJSON(mapBounds, {
          style: (f) => {
            const id = f.properties.jd_field_id;
            if (selIds.has(id)) return { color: "#ffe600", weight: 3, opacity: 0.97, fill: false };
            if (shown.has(id)) return { color: "#8be04e", weight: 1.6, opacity: 0.92, fill: true, fillColor: "#8be04e", fillOpacity: 0.07 };
            return { color: "#8be04e", weight: 0.8, opacity: 0.34, fill: false };
          },
          onEachFeature: (f, lyr) => {
            const p = f.properties || {};
            const mv = meanBy[p.jd_field_id];
            // No sticky label over a field whose map is painted: the live readout
            // already names it AND gives the value at the cursor, and two things
            // chasing the pointer is what made this feel stuck. Fields with no map
            // drawn keep their label — it's the only thing identifying them.
            if (!drawnIds.has(p.jd_field_id)) {
              lyr.bindTooltip(`${p.field || "Field"}${p.acres != null ? ` · ${Number(p.acres).toFixed(1)} ac` : ""}` +
                (mv != null ? `<br><b>${mv}${unit}</b>` : ""), { sticky: true, direction: "top" });
            }
            // Clicking a boundary picks that field — the map is a way INTO the list,
            // not just a picture of what the list already chose.
            lyr.on("click", () => {
              // The year and crop chips decide which pass a tap opens (Kevin, 2026-10-31):
              // tapping a field with 2026 corn picked used to open its newest pass of
              // anything. Only a field with nothing under the chips falls back to all.
              const isF = (g) => g.field_id && g.field_id === p.jd_field_id;
              const opsF = mapGridsF.filter(isF);
              const ops = opsF.length ? opsF : (mapGrids || []).filter(isF);
              if (!ops.length) return;
              // A tap INSIDE the field already open is a read, not a re-pick — it
              // must not throw away the pass the reader chose.
              if (String(p.jd_field_id) === String(mapField)) return;
              const best = jdPickFieldPass(ops, p.jd_field_id, null, null).hit || ops[0];
              setOpenFieldName(null);
              // ⚠ A FAINT OUTLINE HAS NO MAP UNDER THE CHIPS, so its pass comes from another
              // crop or year — and the chips move to it. Left where they were, the
              // follow-the-filter rule saw the field missing from view and jumped the
              // selection to an unrelated field.
              if (!opsF.length) { setCropFilter(best.crop || ""); setYearFilter(best.year ? String(best.year) : ""); }
              setMapField(best.field_id || best.op_id); setMapSel(best.op_id);
            });
          },
        }).addTo(map);
      }

      if (mapMode === "compare") {
        if (!mapCmp || !mapCmp.grids.length) { setMapAuto(null); return; }
        const auto = jdSharedScaleOf(mapCmp.grids);
        if (!auto) { setMapAuto(null); return; }
        setMapAuto(a => (a && a.lo === auto.lo && a.hi === auto.hi) ? a : auto);
        const lo = (mapScale && mapScale.lo != null) ? mapScale.lo : auto.lo;
        const hi = (mapScale && mapScale.hi != null) ? mapScale.hi : auto.hi;
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (const g of mapCmp.grids) {
          const lyr = contour(g, lo, hi, jdOpsColor);
          if (lyr) { lyr.addTo(map); mapCmpRef.current.push(lyr); }
          const b = g.bbox; if (!b) continue;
          if (b[0] < x0) x0 = b[0]; if (b[1] < y0) y0 = b[1]; if (b[2] > x1) x1 = b[2]; if (b[3] > y1) y1 = b[3];
        }
        const fitKey = `cmp:${mapCmp.combo.key}`;
        if (mapFitRef.current !== fitKey && isFinite(x0)) {
          try { map.fitBounds([[y0, x0], [y1, x1]], { padding: [24, 24] }); } catch (e) {}
          mapFitRef.current = fitKey;
        }
        return;
      }

      const g = (mapGrids || []).find(x => x.op_id === mapSel);
      // ZOOM TO THE FIELD AS SOON AS IT IS CHOSEN, before its pixels arrive and even if
      // they never do. The fit below waits on `values`, so a slow or missing map left the
      // reader on the whole region at zoom 8 (Kevin, 2026-10-31: "It needs to zoom to
      // that field"). The pass's own bbox first, else the field's JD boundary.
      if (g && g.bbox && !g.values && mapFitRef.current !== `pre:${mapSel}` && mapFitRef.current !== mapSel) {
        try { map.fitBounds([[g.bbox[1], g.bbox[0]], [g.bbox[3], g.bbox[2]]], { padding: [20, 20] }); } catch (e) {}
        mapFitRef.current = `pre:${mapSel}`;
      } else if (!g && mapField && mapBoundsRef.current && mapFitRef.current !== `fld:${mapField}`) {
        const sel = mapBoundsRef.current.getLayers().filter(l => l.feature && l.feature.properties
          && String(l.feature.properties.jd_field_id) === String(mapField));
        if (sel.length) {
          try { map.fitBounds(L.featureGroup(sel).getBounds(), { padding: [20, 20] }); } catch (e) {}
          mapFitRef.current = `fld:${mapField}`;
        }
      }
      if (!g || !g.values || !g.bbox) { setMapAuto(null); return; }
      // Same correction as compare, for the same reason — one field's raw cells
      // carry the same start/stop tails, they were just less visible when the
      // ramp only had to cover one distribution.
      const auto = jdFieldScaleOf(g);
      if (!auto) { setMapAuto(null); return; }
      setMapAuto(a => (a && a.lo === auto.lo && a.hi === auto.hi) ? a : auto);
      const lo = (mapScale && mapScale.lo != null) ? mapScale.lo : auto.lo;
      const hi = (mapScale && mapScale.hi != null) ? mapScale.hi : auto.hi;
      const lyr = contour(g, lo, hi, jdYieldColor);
      if (lyr) { lyr.addTo(map); mapLayerRef.current = lyr; }
      if (mapFitRef.current !== mapSel) {
        try { map.fitBounds([[g.bbox[1], g.bbox[0]], [g.bbox[3], g.bbox[2]]], { padding: [20, 20] }); } catch (e) {}
        mapFitRef.current = mapSel;
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [view, mapSel, mapGrids, mapScale, mapMode, mapCmp, mapBounds, mapGridsF, mapField]); // eslint-disable-line
  useEffect(() => { setMapScale(null); }, [mapSel, mapCombo, mapMode]); // new field/op/combo → drop the manual scale
  // ── THE SELECTION FOLLOWS THE FILTER ────────────────────────────────────────
  // See the note above: an auto-picked field that the current filter excludes must move to
  // one the filter includes, or the page dead-ends on a farm that has maps.
  useEffect(() => {
    if (!mapGridsF) return;
    // ⚠ THE DRAWN PASS FOLLOWS THE CHIPS TOO, not only the field. On a field's 2026 map,
    // tapping "2025" kept the field (it has 2025 passes) and so kept painting the 2026
    // pass: `curGrid` and the draw read the pass from the unfiltered list, and the Pass
    // dropdown named a 2025 map that was not the one on screen. When the field is still
    // in view and its pass is not, the field's best pass under the chips takes over.
    const keyOf = (g) => g.field_id || g.op_id;
    const inView = mapGridsF.filter((g) => keyOf(g) === mapField);
    if (inView.length) {
      if (!inView.some((g) => g.op_id === mapSel)) {
        const { hit } = jdPickFieldPass(inView, inView[0].field_id, null, null);
        setMapSel((hit || inView[0]).op_id);
      }
      return;
    }
    // A field the user OPENED BY NAME keeps its own answer, including "no map for this
    // one" — silently swapping in another field's map under that name would be worse
    // than the dead end.
    if (openFieldName) return;
    if (!mapGridsF.length) return;
    const g0 = mapGridsF[0];
    const f0 = mapGridsF.filter((g) => keyOf(g) === keyOf(g0));
    const p0 = (g0.field_id ? jdPickFieldPass(f0, g0.field_id, null, null).hit : null) || g0;
    setMapField(keyOf(g0));
    // ⚠ THE OP MOVES WITH THE FIELD. `mapSel` drives the draw effect and the pixel load;
    // leaving it on the previous field's operation would paint the OLD field's map under
    // the new field's name, which is a wrong picture rather than a missing one.
    setMapSel(p0.op_id);
  }, [mapGridsF, mapField, mapSel, openFieldName]); // eslint-disable-line
  // A PHONE HAS NO MOUSE-OUT, so a tapped spot's readout stayed up over the next field's
  // map. A new field, pass or view clears it.
  useEffect(() => { setMapHover(null); }, [mapSel, mapMode, mapCombo, mapField]);
  // (Opened from a field: the grid load runs jdPickFieldPass on the request kept in
  // openReqRef — the field's own crop-year, harvest first, newest first.)
  const plantPickedRef = useRef(false);
  // OPENED FROM A FIELD FOR ITS PLANTING MAP (the Season home's crop page on Planting):
  // the harvest-first pick through openReqRef would land on the yield map, so a planting
  // request picks the field's planting pass instead, in the crop year it was opened on.
  useEffect(() => {
    if (!initialFieldId || !mapGrids || initialMetric !== "seed_rate") return;
    // The same rule as every other way in (jdPickFieldPass: the crop-year asked for, then
    // the newest), over this field's planting passes only. Grid years are crop years, so a
    // fall-sown wheat's drill map already sits in the year it was opened on.
    const plant = mapGrids.filter(g => g.metric === "seed_rate" || g.metric === "coverage");
    const { hit: pick } = jdPickFieldPass(plant, initialFieldId, initialCrop ? String(initialCrop).toUpperCase() : null, initialYear != null ? String(initialYear) : null);
    if (!pick) return;
    // Once only, and over the harvest pick the grid load just made; after that the
    // reader's own choice of pass stands.
    if (plantPickedRef.current) return;
    plantPickedRef.current = true;
    setMapSel(pick.op_id);
  }, [mapGrids, initialFieldId]); // eslint-disable-line
  // OPENED FOR PLANTING MAPS, farm-wide (the Season home's Planting maps button): land on a
  // planting pass of the chosen crop and year, not on the first harvest map the load picked.
  const metricPickedRef = useRef(false);
  useEffect(() => {
    if (metricPickedRef.current || initialFieldId || initialMetric !== "seed_rate" || !mapGrids) return;
    metricPickedRef.current = true;
    const g = mapGridsF.find(x => x.metric === "seed_rate") || mapGridsF.find(x => x.metric === "coverage");
    if (g) { setMapField(g.field_id || g.op_id); setMapSel(g.op_id); }
  }, [mapGrids, mapGridsF]); // eslint-disable-line
  useEffect(() => () => { if (mapObjRef.current) { try { mapObjRef.current.remove(); } catch (e) {} mapObjRef.current = null; } }, []);
  useEffect(() => {
    let alive = true; setData(null); setErr(null);
    authedFetch(`/api/market?fn=jd-fielddata&farmer=${farmerId || farmer.id}`)
      .then(r => r.json())
      .then(d => { if (alive) { if (d && d.error) setErr(d.error); else setData(d); } })
      .catch(e => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [farmerId, farmer.id, calNonce]);
  // A PHONE OPENS ON THE LATEST SEASON, not on every field-year since 2012 (Lawnel 2
  // is ~1,500 field-year rows). Once, on the first load, and only when nothing was
  // chosen: the newest crop year with a harvest in it, else the newest year at all.
  // The year chips sit right above the cards, so "All years" is one tap back.
  const yearDefaultedRef = useRef(false);
  useEffect(() => {
    if (yearDefaultedRef.current || !data || !isMobile || initialFieldId) return;
    yearDefaultedRef.current = true;
    const rows = (data.machine && data.machine.byField) || [];
    const yrs = (pred) => rows.filter(pred).map(r => Number(r.year)).filter(y => y > 0 && y <= new Date().getFullYear());
    const harv = yrs(r => r.harv_ac > 0), any = yrs(() => true);
    const y = harv.length ? Math.max(...harv) : (any.length ? Math.max(...any) : null);
    if (y) setYearFilter(cur => cur || String(y));   // a year already chosen stands
  }, [data]); // eslint-disable-line
  useEffect(() => {   // Escape closes the pop-up / sheet, as every other overlay here does
    if (embedded || !onClose) return;
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [embedded, onClose]);
  useEffect(() => {
    // Harvest Watch is a claim read — agency only. Its tab is gone in portal, so
    // this is belt and braces: the render runs on an ADMIN token under preview.
    if (isPortal) return;
    if (view !== "watch" || watch) return;
    let alive = true; setWatchErr(null);
    authedFetch(`/api/market?fn=jd-claimwatch&farmer=${farmerId || farmer.id}`)
      .then(r => r.json())
      .then(d => { if (alive) { if (d && d.error) setWatchErr(d.error); else setWatch((d.farmers && d.farmers[0]) || { areas: [] }); } })
      .catch(e => { if (alive) setWatchErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [view, farmerId, farmer.id, isPortal]); // eslint-disable-line
  const clickSort = (k) => setSort(s => ({ k, d: s.k === k ? -s.d : 1 }));
  const sorted = (rows) => [...rows].sort((a, b) => {
    const av = a[sort.k], bv = b[sort.k];
    if (av == null) return 1; if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.d;
    return String(av).localeCompare(String(bv), undefined, { numeric: true }) * sort.d;
  });
  // ⚠ THE HEADER STICKS, AND IT IS THE WHOLE COMPLAINT. Kevin, 2026-09-29, comparing
  // this page with John Deere's Work Analyzer: *"clean up this UX to more match JD work
  // analyzer page."* The screenshot he sent shows nine columns of numbers with NO
  // headers on screen — they had scrolled out of the 72vh container, so "1381.5" and
  // "238.5" sit in the TOTAL row with nothing saying which is planted and which is
  // harvested. Work Analyzer pins its header and its Totals/Averages band; this now
  // does the same, for every table in this view since they all render through `th`.
  //
  // The background must be OPAQUE — a transparent sticky header lets the rows scroll
  // through it and reads as a rendering fault rather than a header.
  const th = (label, k, right) => <th onClick={() => clickSort(k)} style={{ cursor:"pointer", textAlign:right?"right":"left", padding:"7px 10px", fontSize:10.5, letterSpacing:"0.05em", color:"#60735f", borderBottom:"2px solid #e0e8e0", whiteSpace:"nowrap", userSelect:"none", position:"sticky", top:0, zIndex:3, background:"#fff", boxShadow:"0 1px 0 #e0e8e0" }}>{label}{sort.k === k ? (sort.d > 0 ? " ▲" : " ▼") : <span style={{ opacity:.25 }}> ⇅</span>}</th>;
  // Digits line up in a column only with tabular figures — the same rule the rest of
  // this app already follows wherever numbers stack. Applied to the right-aligned
  // cells, which are exactly the numeric ones.
  const td = (v, extra) => <td style={{ padding:"6px 10px", fontSize:12.5, color:"#2d3f2d", borderBottom:"1px solid #eef2ee",
    ...(extra && extra.textAlign === "right" ? { fontVariantNumeric:"tabular-nums" } : null), ...extra }}>{v}</td>;
  const cropTag = (c) => <span style={{ color:CROP_COLOR[c]||"#2d3f2d", fontWeight:700 }}>{fmCropName(c)}</span>;
  // The totals band, PINNED to the bottom of the scroll container like Work Analyzer's
  // Totals/Averages row. Defined once because three tables here render a footer and the
  // three had already been written out separately — a style copied three times is three
  // things to keep in step by hand, which is the pattern this repo keeps paying for.
  const FOOT = { fontWeight:800, borderTop:"2px solid #cdd9cd", background:"#eef4ec",
                 position:"sticky", bottom:0, zIndex:2 };
  // Group-by dimensions for the area rollup + Machine-vs-FSA compare. "county"
  // reads the county split (populated now); FSN/Tract/Field read the FTF split
  // (fills in as grids are attributed to CLUs → policy_fields Farm-Tract-Field).
  // UNIT is the dimension the insurance actually lives on. FSN/tract/field are FSA's way of
  // cutting the farm up; RMA approves a yield and settles a claim per APH DATABASE, and one
  // FSN can span many of them (Kennedy's 7648 covers 8 of his 13 units), so grouping by farm
  // number answers a different question than the one a renewal asks.
  const AREA_DIMS = [["county","County"],["unit","Unit"],["fsn","FSN"],["tract","Farm-Tract"],["field","Field"]];
  const areaKey = (r, dim) => {
    const c = (r.county||"").toUpperCase();
    // ⚠ THE UNMATCHED TEST IS FIRST, AND THE COUNTY BRANCH USED TO OUTRUN IT.
    // County returned before this line, so unplaceable acres were folded into the county
    // on the ONE grouping the page opens on — and County is where a reader lands without
    // clicking anything. Every other dimension split them correctly, which is exactly why
    // nobody noticed: the per-field view said "ORLEANS · not matched to a CLU 60.1" while
    // County said 124.4 for the same farm, crop and year.
    // (The server used to drop `attributed` from the county rollup as well; both halves
    // had to be fixed, and `check-jd-acre-truth` asserts the pair.)
    // NOT MATCHED and UNASSIGNED are different failures needing opposite fixes, and
    // calling them one thing is how 1,014.9 ac of Panek's 2025 corn vanished. Not
    // matched = the CLU pass never placed these acres on an insured field (rebuild /
    // recompute maps, or the ground isn't insured with us). Unassigned = the field IS
    // matched, but this crop has no unit on the map we hold.
    if (r.attributed === false) return { key:`${c}|__unmatched`, label:`${c} · not matched to a CLU`, unmatched:true };
    if (dim==="county") return { key:c, label:c };
    if (dim==="unit")   return r.unit
      ? { key:`${c}|${r.unit}|${r.unit_carried?"c":"e"}`,
          // A unit carried from another year's field map is LABELLED with that year.
          // policy_fields holds CY2026 only, so every prior-year unit here is carried —
          // and a number that was never on that year's acreage report must never read
          // like one.
          label:`${c} · Unit ${r.unit}${r.unit_carried && r.unit_year ? ` (${r.unit_year} map)` : ""}` }
      : { key:`${c}|__nounit`, label:`${c} · no ${r.crop ? fmCropName(r.crop).toLowerCase() : "crop"} unit on the map` };
    if (dim==="fsn")    return { key:`${c}|${r.fsn}`, label:`${c} · FSN ${r.fsn??"—"}` };
    if (dim==="tract")  return { key:`${c}|${r.fsn}|${r.tract}`, label:`FSN ${r.fsn??"—"} · Tract ${r.tract??"—"}` };
    return { key:`${c}|${r.fsn}|${r.tract}|${r.field_no}`, label:`FSN ${r.fsn??"—"} · T${r.tract??"—"} · Fld ${r.field_no??"—"}` };
  };
  // ── POST-CALIBRATION, ONE ENTRY POINT ────────────────────────────────────────
  // An uncalibrated combine is wrong by a farm-wide multiplier — Kennedy's 2026 wheat read
  // 46.4 acre-weighted on a crop that made ~71. That error cancels out of the RATIOS between
  // fields, so scaling every value by (true farm yield / monitor farm yield) keeps the
  // monitor's judgement about which ground was better and takes the level from a number
  // somebody counted. The factor is computed server-side once; EVERY display path goes
  // through calY() — tables, comparison and the maps — so they cannot disagree about what a
  // bushel is. Seed rate is never scaled: a yield calibration says nothing about a planter.
  const calFor = (crop, year) => (data && data.calibration
    ? data.calibration[`${String(crop||"").toUpperCase()}|${Number(year)}`] : null) || null;
  const calY = (v, crop, year, met) => {
    if (v == null || met === "seed_rate") return v;
    const c = calFor(crop, year);
    return c && c.factor > 0 ? v * c.factor : v;
  };
  const calAny = !!(data && data.calibration && Object.keys(data.calibration).length);
  // What a calibration total is counted in. Silage is weighed in tons and dry beans in
  // pounds; calling either "bushels" asks the grower for the wrong number.
  const calWord = (crop) => (jdIsSilage(crop) ? "tons" : jdIsLbCrop(crop) ? "pounds" : "bushels");
  const calUnit = (crop) => (jdIsSilage(crop) ? "wet ton/ac" : jdIsLbCrop(crop) ? "lb/ac" : "bu/ac");
  const calKeyLabel = (k) => { const [c, y] = String(k).split("|"); return `${fmCropName(c)} ${y || ""}`.trim(); };
  // byFtf now carries this farm's UNATTRIBUTED acres too (one pool for every grouping),
  // so "has rows" no longer means "the CLU pass has run". Ask the question that's
  // actually being asked: has anything been matched to a CLU yet?
  const ftfEmpty = data && !(data.machine.byFtf || []).some(r => r.attributed);
  // ── ONLY WHAT THIS OPERATION GROWS, IN THE YEAR PICKED (Kevin, 2026-10-31: "Can we
  // only show crops the operation grows instead of all crops in our book?"). The chips
  // used to be every crop any source ever named for the farm across every year, PRF
  // included, which is a policy and not a crop. Now: the crops worked or keyed in the
  // chosen year, PRF dropped, and cover and uninsured crops (radish, rapeseed, fescue)
  // folded behind one "Cover crops" chip so the crops that are insured come first.
  // A field row that recorded nothing (jdFieldRowEmpty) names no crop and no year.
  const fieldRowsAll = data ? (data.machine.byField || []) : [];
  const fieldRows = fieldRowsAll.filter(r => !jdFieldRowEmpty(r));
  const grownRows = data ? [
    ...fieldRows, ...(data.machine.byCounty || []), ...(data.machine.byFtf || []),
    ...(data.fsa.byCounty || []), ...(data.fsa.byFtf || []),
  ] : [];
  const notGrown = (c) => /^(PRF|PASTURE|RANGELAND)/i.test(String(c || "").trim());
  const allYears = [...new Set(grownRows.filter(r => r.crop && !notGrown(r.crop)).map(r => r.year)
    .filter(v => v != null))].sort((a, b) => b - a);
  const allCrops = [...new Set(grownRows
    .filter(r => r.crop && !notGrown(r.crop) && (!yearFilter || String(r.year) === String(yearFilter)))
    .map(r => r.crop))].sort((a, b) => fmCropName(a).localeCompare(fmCropName(b)));
  const mainCrops = allCrops.filter(c => !jdCropUninsured(c));
  const coverCrops = allCrops.filter(c => jdCropUninsured(c));
  const cropOk = (c) => !cropFilter || c === cropFilter;
  const yearsKey = allYears.join("|"), cropsKey = allCrops.join("|");
  // ⚠ A YEAR AFTER THIS ONE IS A CROP SOWN THIS FALL. Wheat and triticale drilled from
  // September are next year's crop (api/_jd.js rmaCropYear), so in October Lawnel 2 has
  // a 2027 chip holding only plantings. It is real — that is the acreage the next report
  // is filed on — so it stays, says what it is, and is never the year a page lands on.
  const thisYear = new Date().getFullYear();
  const yearChip = (y) => (Number(y) > thisYear ? `${y} · sown this fall` : String(y));
  const landYears = allYears.filter(y => Number(y) <= thisYear);
  const yearSettled = useRef(false);
  useEffect(() => {
    if (!data || yearSettled.current) return;
    yearSettled.current = true;
    if (yearFilter && allYears.length && !allYears.some(y => String(y) === String(yearFilter))) setYearFilter(String(landYears[0] != null ? landYears[0] : allYears[0]));
  }, [data, yearsKey, yearFilter]); // eslint-disable-line
  // A crop that isn't grown in the newly picked year would leave the page empty with
  // nothing on the chips saying why.
  useEffect(() => {
    if (!data || !cropFilter) return;
    if (!allCrops.includes(cropFilter)) setCropFilter("");
    else if (jdCropUninsured(cropFilter)) setCoverOpen(true);
  }, [data, cropsKey, cropFilter]); // eslint-disable-line
  const yearOk = (y) => !yearFilter || String(y) === String(yearFilter);
  // A row passes when its date span OVERLAPS the window — county rollups cover a
  // range of days, so testing only the first day would drop a county whose planting
  // started before the window and finished inside it. Rows with no date are kept
  // when no window is set, and excluded once one is, so "no date" can't masquerade
  // as "in range".
  const dateOk = (r) => {
    if (!dFrom && !dTo) return true;
    const a = r && r.date, b = (r && r.date_end) || a;
    if (!a) return false;
    if (dFrom && b < dFrom) return false;
    if (dTo   && a > dTo)   return false;
    return true;
  };
  const dateActive = !!(dFrom || dTo);
  // ⚠ BY FIELD CARRIES NO `date`. Its rows hold plant_date and harv_date, so dateOk
  // refused every one and any date window emptied the list. The window tests the date
  // of the work on screen: the planting on Planting, the harvest on Harvest.
  const dateOkField = (r) => dateOk({ date: String((metric === "seed_rate" ? r.plant_date : r.harv_date) || "").slice(0, 10) || null });

  // ── Draft acreage worksheet from JD as-planted ──
  // Builds a REVIEW-ready printable worksheet (NOT a filed FSA-578) of machine
  // planted acres per Farm-Tract-Field (FSA attribution when available, else JD
  // field names) × crop × plant date, with the FSA keyed acres alongside for
  // reconciliation. This is the "fill an acreage report from JD the minute they're
  // done planting" step — an agent-reviewed draft, never an auto-filed federal doc.
  const buildDraftAR = useCallback(() => {
    // Agency only (Kevin, 2026-10-31: "a farmer should not see draft AR button").
    if (!data || isPortal) return;
    const yr = yearFilter || (allYears[0] != null ? String(allYears[0]) : "");
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    // Prefer FSA Farm-Tract-Field seeding rows; fall back to JD field names.
    const ftf = (data.machine.byFtf || []).filter(r => r.metric === "seed_rate" && cropOk(r.crop) && (!yr || String(r.year) === yr));
    const useFtf = ftf.length > 0;
    let lines;
    if (useFtf) {
      lines = ftf.map(r => ({ farm: r.fsn, tract: r.tract, field: r.field_no, county: r.county, crop: r.crop, ac: r.acres, date: null }));
    } else {
      // A PLANTING IS REPORTED UNDER THE NAME IT WAS PLANTED AS. By field folds a CORN
      // planting onto the field's CORN SILAGE chop (BUG-058) and records the planting's
      // own name as `planted_as`. This worksheet is a draft acreage report, and an
      // acreage report is about what was PLANTED — so it reads that name, which is the
      // row this fallback produced before the fold existed. Reading r.crop here would
      // drop every chopped field's planting under CORN and file it as a CORN SILAGE
      // line with no FSA twin under All crops.
      lines = (data.machine.byField || []).filter(r => r.plant_ac > 0 && cropOk(r.planted_as || r.crop) && (!yr || String(r.year) === yr))
        .map(r => ({ farm: null, tract: null, field: r.field, county: null, crop: r.planted_as || r.crop, ac: r.plant_ac, date: r.plant_date }));
    }
    lines.sort((a, b) => String(a.crop).localeCompare(String(b.crop)) || String(a.farm).localeCompare(String(b.farm)) || String(a.field).localeCompare(String(b.field), undefined, { numeric: true }));
    // FSA keyed acres per crop (for reconciliation)
    const fsaByCrop = {};
    (data.fsa.byFtf || []).filter(r => cropOk(r.crop) && (!yr || String(r.year) === yr)).forEach(r => { fsaByCrop[r.crop] = (fsaByCrop[r.crop] || 0) + (Number(r.acres) || 0); });
    const machByCrop = {};
    lines.forEach(l => { machByCrop[l.crop] = (machByCrop[l.crop] || 0) + (Number(l.ac) || 0); });
    const crops = [...new Set([...Object.keys(machByCrop), ...Object.keys(fsaByCrop)])].sort();
    const printDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    const rowsHtml = lines.map(l => `<tr><td>${esc(l.farm ?? "—")}</td><td>${esc(l.tract ?? "—")}</td><td>${esc(l.field ?? "—")}</td><td>${esc(l.county ?? "—")}</td><td class="c" style="color:#2d6a1a;font-weight:700">${esc(fmCropName(l.crop))}</td><td class="r">${l.ac != null ? Number(l.ac).toFixed(1) : "—"}</td><td class="r">${esc(l.date || "—")}</td></tr>`).join("");
    const reconHtml = crops.map(c => { const m = machByCrop[c] || 0, f = fsaByCrop[c] || 0, d = m - f; return `<tr><td class="c" style="font-weight:700">${esc(fmCropName(c))}</td><td class="r">${m.toFixed(1)}</td><td class="r">${f > 0 ? f.toFixed(1) : "—"}</td><td class="r" style="color:${Math.abs(d) < 1 ? "#60735f" : (d > 0 ? "#2d6a2d" : "#b13a24")};font-weight:700">${f > 0 ? (d > 0 ? "+" : "") + d.toFixed(1) : "—"}</td></tr>`; }).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Draft Acreage Worksheet — ${esc(farmer.name)}</title>
<style>
  body { font-family:'Helvetica Neue',Arial,sans-serif; color:#1a2e1a; padding:26px; max-width:900px; margin:0 auto; }
  .banner { background:#fff8ec; border:1px solid #f0dcae; border-radius:8px; padding:10px 14px; font-size:12px; color:#8a5a00; margin-bottom:16px; }
  h1 { font-size:20px; margin:0 0 2px; } .sub { font-size:12.5px; color:#5c6f5c; margin-bottom:4px; }
  .asof { font-size:11px; color:#60735f; }
  h2 { font-size:13px; color:#2d4a2d; margin:22px 0 8px; text-transform:uppercase; letter-spacing:.04em; }
  table { width:100%; border-collapse:collapse; font-size:12px; } th,td { padding:6px 9px; border-bottom:1px solid #eef2ee; text-align:left; }
  th { background:#f4f8f4; color:#5a7a5a; font-size:10px; text-transform:uppercase; letter-spacing:.05em; }
  td.r,th.r { text-align:right; font-family:monospace; } td.c { }
  .print-btn { float:right; background:#2d5a10; color:#fff; border:none; border-radius:8px; padding:9px 16px; font-size:13px; font-weight:700; cursor:pointer; }
  .footer { margin-top:22px; padding-top:12px; border-top:1px solid #d6e0d6; font-size:10.5px; color:#60735f; }
  @media print { .no-print { display:none !important; } body { padding:0; } }
</style></head><body>
  <button class="print-btn no-print" onclick="window.print()">Print / Save PDF</button>
  <h1>Draft Acreage Worksheet</h1>
  <div class="sub">${esc(farmer.name)}${yr ? " · " + esc(yr) : ""} · from John Deere Operations Center as-planted</div>
  <div class="asof">Machine planted acres (deduped actual coverage) · Generated ${printDate}</div>
  <div class="banner" style="margin-top:14px"><b>DRAFT — agent review required.</b> These are machine-derived planted acres from the grower's John Deere account, ${useFtf ? "matched to FSA Farm-Tract-Field via insured CLUs" : "shown by John Deere field name (FSA Farm-Tract-Field attribution still computing)"}. Verify against the grower's records before keying to the AIP or filing FSA-578. Not a filed federal document.</div>
  <h2>Reconciliation — machine vs FSA keyed acres</h2>
  <table><thead><tr><th>Crop</th><th class="r">Machine planted ac</th><th class="r">FSA keyed ac</th><th class="r">Δ</th></tr></thead><tbody>${reconHtml || '<tr><td colspan="4" style="color:#677167">No crop totals</td></tr>'}</tbody></table>
  <h2>Detail — ${useFtf ? "by Farm-Tract-Field" : "by field"}</h2>
  <table><thead><tr><th>Farm</th><th>Tract</th><th>Field</th><th>County</th><th>Crop</th><th class="r">Planted ac</th><th class="r">Plant date</th></tr></thead><tbody>${rowsHtml || '<tr><td colspan="7" style="color:#677167">No planting data for this selection</td></tr>'}</tbody></table>
  <div class="footer">Clover Ag Risk Management · (585) 233-1066 · Draft acreage worksheet generated from John Deere machine data · ${printDate}</div>
  <script>window.addEventListener('load',function(){setTimeout(function(){try{window.print();}catch(e){}},500);});</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Popup blocked — allow popups for this site, then click again."); return; }
    writePrintWindow(w, html);
  }, [data, yearFilter, allYears, cropFilter, farmer.name, isPortal]); // eslint-disable-line

  // The views, once — the desktop tab row and the phone chip row both read this, so the
  // agency-only pair can only ever be gated in one place (check-machine-data-portal).
  const VIEWS = [["maps","Maps"],["field","By field"],["area","By area"],
    // Machine vs FSA is the farmer's OWN acres against their OWN policy, so a grower sees it
    // (Kevin, 2026-10-31: "Yes"). Harvest Watch is a claim read and stays agency only.
    ["compare","Machine vs FSA"], ...(isPortal ? [] : [["watch","Harvest Watch"]])];
  // Planting has no Yield/Production column, so a sort left over from Harvest would
  // order the table by a column that isn't on screen.
  const pickMetric = (k) => { setMetric(k); if (k === "seed_rate") setSort(s => (s.k === "mean" || s.k === "prod") ? { k:"acres", d:-1 } : s); };
  // Same chip as the Field Mapper home sheet, so the two pages read as one app.
  const mChip = (on) => ({ flexShrink:0, cursor:"pointer", border: on ? "1px solid transparent" : "1px solid #cdd9cd", borderRadius:18, height:36, padding:"0 12px",
    fontSize:12.5, fontWeight:800, fontFamily:"inherit", whiteSpace:"nowrap", background: on ? T.ink : "#fff", color: on ? "#a8d878" : T.ink });
  const mRow = { display:"flex", gap:6, overflowX:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none", padding:"1px 0 3px" };
  // ⚠ THE BADGE COUNTS ONLY WHAT ACTS ON THIS SCREEN. It counted a date window and a
  // field search on every tab, and "Calibrated" where no calibration control shows, so
  // "Filters · 2" sat over a map neither filter touched.
  const dateApplies = view === "field" || view === "area" || view === "compare";
  const calShown = (view === "area" || view === "compare") && metric === "yield";
  const moreCount = (dateActive && dateApplies ? 1 : 0) + (fieldQ && view === "field" ? 1 : 0) + (calAny && calShown ? 1 : 0);
  const inner = (
    <>
        {/* On a phone the sheet's own Back bar names the farm; a second header under it
            is a row of nothing. Inside Field Mapper the page header already says
            "Machine data" and the farm, so the phone hides this one there too — Kevin,
            2026-10-31, saw it twice in a row. */}
        {!isMobile && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", background:"#fff", color:T.ink, borderBottom:"1px solid #eef2ee" }}>
          <div style={{ minWidth:0 }}>
            <div style={{ fontFamily:T.serif, fontSize:21, fontWeight:700, lineHeight:1.15 }}>Machine data</div>
            <div style={{ fontSize:13, color:"#4f6a4c", marginTop:2 }}>{farmer.name}</div>
          </div>
          {!embedded && <button onClick={onClose} aria-label="Close machine data"
            style={{ width:36, height:36, borderRadius:18, border:"1px solid #dfe7de", background:"#f4f8f3", color:T.ink, fontSize:18, fontWeight:700, lineHeight:1, cursor:"pointer", fontFamily:"inherit" }}>×</button>}
        </div>
        )}
        {/* ── THE PHONE BAR ─────────────────────────────────────────────────────
            The desktop bar wrapped into ~6 rows on a phone before a single field showed
            (Kevin, 2026-10-31). Here: what to look at, which year, which crop, each a
            row of chips that swipes sideways, and Harvest/Planting where it changes
            the numbers. Everything else (date window, search, grouping, calibration,
            Draft AR) folds behind Filters and is the SAME desktop controls. */}
        {isMobile && (
          <div style={{ padding:"10px 10px 8px", display:"flex", flexDirection:"column", gap:7, borderBottom:"1px solid #eef2ee" }}>
            <div style={mRow}>{VIEWS.map(([k,lab]) => <button key={k} onClick={() => setView(k)} style={mChip(view===k)}>{lab}</button>)}</div>
            <div style={mRow}>
              <button onClick={() => setYearFilter("")} style={mChip(!yearFilter)}>All years</button>
              {allYears.map(y => <button key={y} onClick={() => setYearFilter(String(y))} style={mChip(String(yearFilter) === String(y))}>{yearChip(y)}</button>)}
            </div>
            <div style={mRow}>
              <button onClick={() => setCropFilter("")} style={mChip(!cropFilter)}>All crops</button>
              {mainCrops.map(c => <button key={c} onClick={() => setCropFilter(c)} style={mChip(cropFilter === c)}>{fmCropName(c)}</button>)}
              {coverCrops.length > 0 && (
                <button onClick={() => setCoverOpen(o => !o)} aria-expanded={coverOpen}
                  style={{ ...mChip(coverOpen && !cropFilter), borderStyle:"dashed" }}>Cover crops · {coverCrops.length} {coverOpen ? "▴" : "▾"}</button>
              )}
              {coverOpen && coverCrops.map(c => <button key={c} onClick={() => setCropFilter(c)} style={mChip(cropFilter === c)}>{fmCropName(c)}</button>)}
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              {view !== "maps" && view !== "watch" && (
                <div style={{ display:"flex", flex:1, background:"#e2eae1", borderRadius:9, padding:3, gap:3 }}>
                  {[["yield","Harvest"],["seed_rate","Planting"]].map(([k,lab]) => (
                    <button key={k} onClick={() => pickMetric(k)}
                      style={{ flex:1, cursor:"pointer", border:"none", borderRadius:7, padding:"7px 0", fontSize:12.5, fontFamily:"inherit",
                               fontWeight: metric===k ? 800 : 700, background: metric===k ? "#fff" : "transparent", color: metric===k ? "#1a2e1a" : "#2d4a2d",
                               boxShadow: metric===k ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>{lab}</button>
                  ))}
                </div>
              )}
              <button onClick={() => setMoreOpen(o => !o)} style={{ ...mChip(moreOpen), marginLeft:"auto" }}>
                Filters{moreCount ? ` · ${moreCount}` : ""} {moreOpen ? "▴" : "▾"}
              </button>
            </div>
          </div>
        )}
        {(!isMobile || moreOpen) && (
        <div style={{ padding: isMobile ? "10px" : "12px 18px", display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", borderBottom:"1px solid #eef2ee", background: isMobile ? "#fafcfa" : undefined }}>
          {!isMobile && VIEWS.map(([k,lab]) => (
            <button key={k} onClick={() => setView(k)} style={{ cursor:"pointer", border:"1px solid #cdd9cd", borderRadius:7, padding:"6px 12px", fontSize:12, fontWeight:700, background: view===k?"#2d4a2d":"#f6faf6", color: view===k?"#fff":"#2d4a2d" }}>{lab}</button>
          ))}
          {!isMobile && <select value={cropFilter} onChange={e => setCropFilter(e.target.value)} title="Filter to one crop"
            style={{ fontSize:12, fontWeight:700, padding:"6px 8px", borderRadius:7, border:"1px solid #cdd9cd", background:"#fff", color:"#2d4a2d", cursor:"pointer" }}>
            <option value="">All crops</option>
            {mainCrops.map(c => <option key={c} value={c}>{fmCropName(c)}</option>)}
            {coverCrops.length > 0 && <optgroup label="Cover and uninsured crops">{coverCrops.map(c => <option key={c} value={c}>{fmCropName(c)}</option>)}</optgroup>}
          </select>}
          {!isMobile && <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} title="Filter to one crop year — FSA keyed acres are the current crop year; machine data is the year each field was worked"
            style={{ fontSize:12, fontWeight:700, padding:"6px 8px", borderRadius:7, border:"1px solid #cdd9cd", background:"#fff", color:"#2d4a2d", cursor:"pointer" }}>
            <option value="">All years</option>
            {allYears.map(y => <option key={y} value={y}>{yearChip(y)}</option>)}
          </select>}

          {/* Plant/harvest DATE window — filters on the operation date itself rather
              than the derived crop year. Applies to the machine side only: FSA keyed
              acres carry no operation date, so filtering them would blank the
              comparison column. */}
          {dateApplies && (() => {
            const di = { fontSize:11.5, padding:"5px 6px", borderRadius:6, border:`1px solid ${dateActive ? "#2d4a2d" : "#cdd9cd"}`,
                         background:"#fff", color:"#2d4a2d", fontFamily:"inherit" };
            return (
              <span style={{ display:"inline-flex", alignItems:"center", gap:5, marginLeft:4 }}
                title="Filter by the date the planter or combine actually ran. Crop year is derived and a fall-seeded crop spans two calendar years — the operation date doesn't move.">
                <span style={{ fontSize:10.5, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"#60735f" }}>
                  {metric === "seed_rate" ? "Planted" : "Harvested"}
                </span>
                <input type="date" value={dFrom} onChange={e => setDFrom(e.target.value)} style={di} aria-label="from date" />
                <span style={{ fontSize:11, color:"#677167" }}>→</span>
                <input type="date" value={dTo} onChange={e => setDTo(e.target.value)} style={di} aria-label="to date" />
                {dateActive && (
                  <button onClick={() => { setDFrom(""); setDTo(""); }} title="Clear the date window"
                    style={{ border:"none", background:"none", cursor:"pointer", color:"#2d6a2d", fontWeight:800, fontSize:14, lineHeight:1, padding:"0 2px" }}>×</button>
                )}
              </span>
            );
          })()}
          {/* SEARCH BY FIELD NAME — Work Analyzer leads with one and this page had none.
              Only on By field, because it narrows that table's rows and showing it over
              a table it does not filter is worse than not having it. */}
          {view === "field" && (
            <span style={{ display:"inline-flex", alignItems:"center", gap:4, marginLeft:6 }}>
              <input value={fieldQ} onChange={e => setFieldQ(e.target.value)} placeholder="Search field…"
                aria-label="search field name"
                style={{ fontSize:11.5, padding:"5px 8px", borderRadius:6, width:132, fontFamily:"inherit",
                         border:`1px solid ${fieldQ ? "#2d4a2d" : "#cdd9cd"}`, background:"#fff", color:"#2d4a2d" }} />
              {fieldQ && (
                <button onClick={() => setFieldQ("")} title="Clear the field search"
                  style={{ border:"none", background:"none", cursor:"pointer", color:"#2d6a2d", fontWeight:800, fontSize:14, lineHeight:1, padding:"0 2px" }}>×</button>
              )}
            </span>
          )}
          {(view==="area" || view==="compare") && (
            <>
              <span style={{ marginLeft:8, display:"inline-flex", gap:4, alignItems:"center" }}>
                <span style={{ fontSize:10.5, color:"#677167", fontWeight:700, marginRight:2 }}>GROUP</span>
                {AREA_DIMS.map(([k,lab]) => (
                  <button key={k} onClick={() => setGroupBy(k)}
                    title={isPortal
                      ? (k === "unit" ? "The insurance unit each field is reported under" : k !== "county" ? "Your FSA farm, tract and field numbers, matched to the machine data by field boundary" : undefined)
                      : (k === "unit" ? "The APH database RMA rates, from the policy's unit numbers, matched to machine data by CLU — one FSN can span several units, so this is not the same cut as Farm-Tract."
                         : k !== "county" ? "From FSA Farm-Tract-Field on the policy, matched to machine data by CLU" : undefined)}
                    style={{ cursor:"pointer", border:"none", borderRadius:6, padding:"5px 9px", fontSize:11.5, fontWeight:700, background: groupBy===k?"#2d4a2d":"#eef2ee", color: groupBy===k?"#fff":"#2d4a2d" }}>{lab}</button>
                ))}
              </span>
              {/* POST-CALIBRATION. Lives beside the grouping because this is where a yield
                  number is being read, and a scaled number must never be mistaken for a raw
                  one. Entering the true total rescales the tables AND the maps together. */}
              {/* A GROWER MAY POST-CALIBRATE THEIR OWN CROP (Kevin, 2026-08-24).
                  This was agency-only for a day, because `yield_calibrations`
                  carried a single is_admin() policy while preview-as runs the
                  GROWER's session — so the write was refused by the database and
                  a raw PostgREST 42501 landed on screen.

                  sql/049 gives a grower select + insert + update on their OWN
                  farmer_id through portal_has_farmer(), and deliberately NO
                  delete: correcting your own figure is a click, removing one an
                  agent entered is a conversation. Proven both ways by
                  rls_selftest_yield_calibrations(), which WRITES its own test row
                  rather than waiting for one to exist — the manifest loop in
                  rls_selftest() can only test a table that already holds a
                  portal-owned row, so a brand-new capability would have read VOID
                  there until the day somebody used it.

                  The control is identical on both sides; only the copy differs,
                  because the consequence differs — see the panel. */}
              {metric === "yield" && (
                <span style={{ display:"inline-flex", gap:5, alignItems:"center" }}>
                  <button onClick={() => setCalOpen(o => !o)}
                    title="Scale an uncalibrated monitor to what the crop actually made"
                    style={{ cursor:"pointer", borderRadius:6, padding:"5px 9px", fontSize:11.5, fontWeight:700,
                             border: calAny ? "1px solid #2d6a2d" : "1px solid #dde5dd",
                             background: calAny ? "#eef6ec" : "#fff", color: calAny ? "#1f5c2e" : "#5c6f5c" }}>
                    {calAny ? "Calibrated" : "Calibrate…"}
                  </button>
                  {calAny && (() => {
                    const k = Object.keys(data.calibration)[0], c = data.calibration[k];
                    return <span style={{ fontSize:10.5, color:"#2d5a10", fontWeight:700 }}
                                 title={`Monitor read ${c.monitor_yield}; ${c.total_bushels.toLocaleString()} ${calWord(k.split("|")[0])} over ${c.acres} ac = ${c.target_yield}. Coverage ${c.coverage_pct ?? "?"}%.`}>
                      ×{c.factor} · {calKeyLabel(k)}{Object.keys(data.calibration).length > 1 ? ` +${Object.keys(data.calibration).length - 1}` : ""}
                    </span>;
                  })()}
                </span>
              )}
              {calOpen && metric === "yield" && (
                <div style={{ flexBasis:"100%", marginTop:6, border:"1px solid #cfe0cf", borderRadius:9, background:"#fbfdfb", padding:"9px 11px" }}>
                  <div style={{ fontSize:10.5, textTransform:"uppercase", letterSpacing:".04em", color:"#60735f", fontWeight:700, marginBottom:6 }}>
                    Post-calibrate from the total harvested
                  </div>
                  <div style={{ fontSize:11.5, color:"#5c6f5c", marginBottom:8, maxWidth:640, lineHeight:1.5 }}>
                    An uncalibrated combine is wrong by the same multiple everywhere, so the map's <b>pattern</b> is
                    still right — only the level is off. Give the true total and every yield here and on the maps is
                    rescaled to it.
                    {/* SAY THE CONSEQUENCE OUT LOUD. The factor is stored once and
                        read by api/_jd.js with the SERVICE ROLE for every caller,
                        so a grower's entry moves the numbers on the agency's
                        screens too. That is not a side effect to discover later —
                        it is the reason to get the total right. */}
                    {isPortal && <> Your agent sees the same rescaled numbers, so enter a total you would stand
                    behind — scale tickets or the elevator settlement, not an estimate.</>}
                  </div>
                  <div style={{ display:"flex", gap:7, alignItems:"center", flexWrap:"wrap" }}>
                    <select value={calForm.crop} onChange={e => setCalForm(f => ({ ...f, crop:e.target.value }))}
                            style={{ fontSize:12, padding:"5px 7px", borderRadius:6, border:"1px solid #d5e2d5" }}>
                      <option value="">crop…</option>
                      {mainCrops.map(c => <option key={c} value={c}>{fmCropName(c)}</option>)}
            {coverCrops.length > 0 && <optgroup label="Cover and uninsured crops">{coverCrops.map(c => <option key={c} value={c}>{fmCropName(c)}</option>)}</optgroup>}
                    </select>
                    <select value={calForm.year} onChange={e => setCalForm(f => ({ ...f, year:e.target.value }))}
                            style={{ fontSize:12, padding:"5px 7px", borderRadius:6, border:"1px solid #d5e2d5" }}>
                      <option value="">year…</option>
                      {allYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <input type="number" value={calForm.bushels} placeholder={`total ${calWord(calForm.crop)}`}
                           onChange={e => setCalForm(f => ({ ...f, bushels:e.target.value }))}
                           style={{ width:130, fontSize:12, padding:"5px 7px", borderRadius:6, border:"1px solid #d5e2d5", textAlign:"right" }} />
                    <select value={calForm.source} onChange={e => setCalForm(f => ({ ...f, source:e.target.value }))}
                            style={{ fontSize:12, padding:"5px 7px", borderRadius:6, border:"1px solid #d5e2d5" }}>
                      {/* Where the number came from — "scale tickets" and "grower said so"
                          should not look identical to whoever reads this next season. */}
                      {["scale tickets","elevator settlement","grower stated","other"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button disabled={calBusy || !calForm.crop || !calForm.year || !(+calForm.bushels > 0)}
                      onClick={async () => {
                        setCalBusy(true); setCalErr(null);
                        try {
                          await sbUpsert("yield_calibrations", [{
                            farmer_id: farmerId, crop_year: Number(calForm.year), crop: String(calForm.crop).toUpperCase(),
                            county: null, total_bushels: Number(calForm.bushels), source: calForm.source,
                            updated_at: new Date().toISOString(),
                          }], "farmer_id,crop_year,crop");
                          setCalOpen(false); setCalForm(f => ({ ...f, bushels:"" }));
                          setCalNonce(n => n + 1);   // re-pull so the factor lands on every table at once
                        } catch (e) {
                          // A DATABASE REFUSAL IS NOT A SENTENCE ANYBODY CAN ACT ON,
                          // and `sbUpsert` throws the raw PostgREST body. Name the one
                          // cause an agent actually hits — the wrong IDENTITY, not the
                          // wrong data — and keep the body in the console, where it is
                          // written for us rather than for whoever is watching.
                          const raw = String((e && e.message) || e);
                          console.error("yield_calibrations upsert failed:", raw);
                          setCalErr(/42501|row-level security/i.test(raw)
                            ? "The database refused this write — this login isn't granted access to this farm. Nothing was saved."
                            : "Couldn't save the calibration — the details are in the browser console.");
                        }
                        setCalBusy(false);
                      }}
                      style={{ fontSize:12, fontWeight:800, padding:"6px 14px", borderRadius:6, border:"1px solid #2d6a2d",
                               background: calBusy ? "#7fa87f" : "#2d6a2d", color:"#fff", cursor: calBusy ? "default" : "pointer" }}>
                      {calBusy ? "saving…" : "Apply"}
                    </button>
                    <button onClick={() => { setCalOpen(false); setCalErr(null); }}
                            style={{ fontSize:11.5, padding:"5px 10px", borderRadius:6, border:"1px solid #dde5dd", background:"#fff", color:"#5c6f5c", cursor:"pointer" }}>cancel</button>
                  </div>
                  {calErr && <div style={{ marginTop:7, fontSize:11.5, color:"#a5461b" }}>{calErr}</div>}
                  {calAny && (
                    <div style={{ marginTop:8, fontSize:11.5, color:"#3a4a3a" }}>
                      {Object.entries(data.calibration).map(([k, c]) => (
                        <div key={k} style={{ marginTop:2 }}>
                          <b>{calKeyLabel(k)}</b> — monitor {c.monitor_yield} → <b style={{ color:"#1f5c2e" }}>{c.target_yield}</b> {calUnit(k.split("|")[0])}
                          {" "}(×{c.factor}, {Number(c.total_bushels).toLocaleString()} {calWord(k.split("|")[0])} over {c.acres} ac
                          {c.coverage_pct != null ? `, monitor saw ${c.coverage_pct}%` : ""}{c.source ? ` · ${c.source}` : ""})
                          {/* WHO SET IT, not just where the number came from.
                              `source` is a free choice on a dropdown; this is
                              stamped by the database from the JWT (sql/049) and
                              cannot be sent by the client. An unrecorded author
                              says so rather than defaulting to either side. */}
                          {c.set_by && <span style={{ color:"#60735f" }}> — set by the {c.set_by}</span>}
                          {c.coverage_pct != null && c.coverage_pct < 85 && (
                            <span style={{ color:"#a5461b", fontWeight:700 }}> — monitor covered only {c.coverage_pct}% of reported acres, so this factor assumes the unseen ground matched the seen ground.</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!isMobile && <span style={{ display:"inline-flex", gap:4, alignItems:"center" }}>
                <span style={{ fontSize:10.5, color:"#677167", fontWeight:700, marginRight:2 }}>OP</span>
                {[["seed_rate","Planting"],["yield","Harvest"]].map(([k,lab]) => (
                  // Planting has no Yield/Production column, so a sort left over from
                  // Harvest would order the table by a column that isn't on screen.
                  <button key={k} onClick={() => pickMetric(k)} style={{ cursor:"pointer", border:"none", borderRadius:6, padding:"5px 10px", fontSize:11.5, fontWeight:700, background: metric===k?"#a8d878":"#eef2ee", color:"#1a2e1a" }}>{lab}</button>
                ))}
              </span>}
            </>
          )}
          {!isPortal && <button onClick={buildDraftAR} disabled={!data} title="Build a review-ready draft acreage worksheet from John Deere as-planted data (not a filed FSA-578)"
            style={{ marginLeft:"auto", cursor: data?"pointer":"default", border:"1px solid #2d5a10", borderRadius:7, padding:"6px 12px", fontSize:12, fontWeight:700, background: data?"#eaf5ea":"#f0f0f0", color:"#2d5a10", opacity: data?1:0.5 }}>Draft AR</button>}
          {!isPortal && <span style={{ fontSize:10.5, color:"#677167" }}>machine acres = actual coverage (deduped)</span>}
        </div>
        )}
        {/* ONE SCROLLER ON A PHONE. This box was a 72vh scroller inside a page that
            scrolls, and on iOS the two fought (Kevin, 2026-10-31: it "didn't scroll").
            On a phone the sheet — or, embedded, the page — does the vertical scrolling;
            this box only lets a wide table slide sideways inside itself. */}
        <div style={isMobile ? { padding:"8px 10px 24px", overflowX:"auto" } : { padding:"4px 8px 18px", maxHeight:"72vh", overflow:"auto" }}>
          {err && view!=="maps" && <div style={{ padding:16, color:"#c0392b", fontSize:13 }}>Couldn't load: {err}</div>}
          {!data && !err && view!=="maps" && <div style={{ padding:24, color:"#60735f", fontSize:13 }}><UiSpinner />Loading John Deere field data…</div>}
          {view==="maps" && (() => {
            const gridsF = mapGridsF;
            // Group by the farmer's JD FIELD (their own name for the ground), not by
            // FTF — so each row leads with the name the farmer speaks, with the
            // Farm-Tract-Field shown underneath as the insurance identity.
            const byField = {};
            for (const g of gridsF) {
              const k = g.field_id || g.op_id;
              const rec = (byField[k] ||= { key:k, field:g.field, ftf_label:g.ftf_label||null, custom_from:g.custom_from||null, custom_role:g.custom_role||null, ops:[] });
              rec.ops.push(g);
              if (!rec.ftf_label && g.ftf_label) rec.ftf_label = g.ftf_label;
            }
            const dispName = jdFieldLabel;
            const fields = Object.values(byField).sort((a,b)=>String(dispName(a)).localeCompare(String(dispName(b)),undefined,{numeric:true}));
            const curField = byField[mapField] || null;
            const curOps = curField ? [...curField.ops].sort((a,b)=>(b.year||0)-(a.year||0) || String(a.metric).localeCompare(String(b.metric))) : [];
            const curGrid = (mapGrids||[]).find(x=>x.op_id===mapSel);
            const counties = [...new Set((mapGrids||[]).map(g=>g.county).filter(Boolean))].sort();
            // NAME THE PASS IN WORDS. This select is the ONLY way to switch between a
            // field's planting map and its harvest map, and an emoji was the only thing
            // that said so — Kevin, 2026-08-21: "when I want to switch from planting to
            // harvest, it's not clearly labeled how to do that."
            const PASS_KIND = { seed_rate: "Planting", coverage: "Planting", yield: "Harvest" };
            const passKind = (g) => PASS_KIND[g && g.metric] || "Pass";
            // THE GRID'S OWN UNIT, never one derived from the metric. Measured
            // 2026-08-21: seed_rate is seeds/ac on 9,848 grids and lb/ac on 1,917, and
            // 8 yield grids are ton/ac — corn SILAGE. The old
            // `metric==="seed_rate" ? "" : " bu/ac"` therefore printed a bare 32534.6
            // for every planting map and "bu/ac" over silage tonnage, which is exactly
            // the unit-of-measure conflation this repo forbids everywhere else.
            const gUnit = (g) => (g && g.unit ? ` ${jdShowUnit(g.unit)}` : (g && g.metric === "yield" ? " (unit not recorded)" : ""));
            // A COVERAGE grid has no rate to average — its cells record only that the
            // planter was there — so it names the pass and prints no number. Averaging
            // it would put a meaningless 1.0 beside two real rates in the same list.
            const opLabel = (g) => `${passKind(g)} · ${g.crop ? fmCropName(g.crop) : "Unknown crop"} ${g.year || ""}`
              + (g.metric !== "coverage" && g.mean != null ? ` · ${fmt(g.mean, g.mean >= 1000 ? 0 : 1)}${gUnit(g)}` : "");
            // Whatever the draw effect actually scaled to. Deliberately not
            // recomputed here: the honest number is the one the picture used, and
            // deriving it a second way is how the two drift apart.
            const autoLo = mapAuto ? mapAuto.lo : 0, autoHi = mapAuto ? mapAuto.hi : 1;
            // ⚠ A FIELD WITH NO MAP MUST SAY SO, NOT RENDER NOTHING. Arriving here from
            // the By-field table selects a field by id; if no grid was ever built for it
            // the field is absent from the list above and the page would draw an empty
            // frame, which reads as a broken link rather than as the fact it is. A
            // machine can work ground and leave no map — a pass with no rate column, an
            // export that never generated — and that is worth stating plainly.
            const openedButUnmapped = mapField != null && !curField && (mapGrids || []).length > 0;
            // ⚠ "NO MAP FOR THIS FIELD" WAS READ AS "NO DATA FOR THIS FARM", AND THE
            // EXPLANATION UNDER IT WAS THE WRONG ONE (Kevin, 2026-09-28: *"is Swede Farms
            // data even in Silas? I see no wheat data for them?"*).
            //
            // GARY SWEDE FARMS is the case. Measured: his CY2026 wheat carries SIX harvest
            // operations, 278.9 ac, 13-16 July, and **all six have a yield grid** — crop
            // WHEAT, bu/ac. Nothing was missing. What had happened is that a field was
            // still selected from another crop, the crop filter was then set to WHEAT, and
            // that field has no wheat map — so the page said "no map was built for this
            // crop and year" over a farm with six of them.
            //
            // The old copy offered two causes — no rate column, export not generated — and
            // omitted the one that actually applies here and is by far the commonest:
            // THIS FIELD DID NOT GROW THAT CROP THAT YEAR, AND OTHERS DID. `fields` is
            // already the list of fields that DO have a map under the current filter, so
            // the count is in hand; stating it turns a dead end into a signpost.
            //
            // ⚠ AND THE ESCAPE HATCH THREW AWAY THE QUESTION. The one button cleared the
            // crop and year filters as well as the field, so a reader who asked for WHEAT
            // 2026 and landed here was returned to "everything" — the filter they had just
            // set, silently discarded. Keeping the filter and dropping only the field is
            // what they meant; clearing everything stays available, second.
            const alsoMapped = fields.length;   // fields WITH a map under this filter
            const filterLabel = `${cropFilter ? fmCropName(cropFilter) : ""}${cropFilter && yearFilter ? " " : ""}${yearFilter || ""}`.trim();
            if (openedButUnmapped) return (
              <div style={{ padding: "18px 4px", fontSize: 12.5, color: "#5a6a5a" }}>
                <div style={{ fontWeight: 800, color: "#8a5a00", marginBottom: 6 }}>
                  No machine map for {openFieldName || "this field"}
                  {cropFilter ? ` · ${fmCropName(cropFilter)}` : ""}{yearFilter ? ` ${yearFilter}` : ""}
                </div>
                <div style={{ marginBottom: 10, maxWidth: 560 }}>
                  {alsoMapped > 0 ? (
                    <>
                      <b>{alsoMapped} other John Deere field{alsoMapped === 1 ? " does" : "s do"}</b> have
                      a {filterLabel || "machine"} map — this one does not, which usually just
                      means it was not in that crop that year. Nothing is missing for this farm.
                      {/* The farmer gets the plain fact; "with us" is the agency talking, and
                          there is no insured field count anywhere above this to compare. */}
                      {isPortal
                        ? <>{" "}A machine can also run on rented or custom ground, so this count can include fields that aren't on your policy.</>
                        : <>{" "}(That is a count of <b>John Deere</b> fields, which can exceed the insured
                      field count: a machine also runs on rented or custom-harvested ground
                      that carries no policy in this book.)</>}
                    </>
                  ) : (
                    <>
                      John Deere holds work on this field, but no map was built for this crop and year.
                      That happens when a pass carried no rate column, or when its export has not
                      generated yet — it is not a statement that the field was unworked.
                    </>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {/* KEEPS the crop/year the reader asked for — only the field selection goes. */}
                  {alsoMapped > 0 && (
                    <button onClick={() => { setMapField(null); setOpenFieldName(null); }}
                            style={{ cursor: "pointer", border: "1px solid #2d4a2d", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 800, background: "#2d4a2d", color: "#fff" }}>
                      ← Show the {alsoMapped} field{alsoMapped === 1 ? "" : "s"}
                      {filterLabel ? ` with a ${filterLabel} map` : " with a map"}
                    </button>
                  )}
                  <button onClick={() => { setMapField(null); setOpenFieldName(null); setCropFilter(""); setYearFilter(""); }}
                          style={{ cursor: "pointer", border: "1px solid #cdd9cd", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 700, background: "#f6faf6", color: "#2d4a2d" }}>
                    ← Show every mapped field
                  </button>
                </div>
              </div>
            );
            return (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                <input value={mapQ} onChange={e=>setMapQ(e.target.value)} placeholder="Search field…"
                  style={{ fontSize: isMobile ? 16 : 12, padding:"6px 10px", borderRadius:7, border:"1px solid #cdd9cd", minWidth:150, flex: isMobile ? "1 1 100%" : "none" }} />
                <select value={mapCounty} onChange={e=>setMapCounty(e.target.value)}
                  style={{ fontSize:12, fontWeight:700, padding:"6px 8px", borderRadius:7, border:"1px solid #cdd9cd", background:"#fff", color:"#2d4a2d", cursor:"pointer" }}>
                  <option value="">All counties</option>
                  {counties.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {[["field","One field","Paint the selected field's map on its own scale"],
                  ["compare","Compare","Every field of one crop-year on ONE shared scale — the only way to see which ground ran behind"]].map(([k,lab,tt]) => (
                  <button key={k} onClick={()=>setMapMode(k)} title={tt}
                    style={{ border:"1px solid "+(mapMode===k?"#1a2e1a":"#cdd9cd"), background: mapMode===k?"#1a2e1a":"#fff",
                             color: mapMode===k?"#a8d878":"#2d4a2d", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{lab}</button>
                ))}
                {mapMode==="compare" && (
                  mapCombos.length ? (
                    <select value={(mapCmp&&mapCmp.combo.key)||""} onChange={e=>setMapCombo(e.target.value)}
                      title="One metric, one crop, one year, one unit. Never blended — corn and soybeans on a shared ramp paints every bean field as a failure, and seeds/ac against lb/ac is not the same quantity at all."
                      style={{ fontSize:12, fontWeight:700, padding:"6px 8px", borderRadius:7, border:"1px solid #cdd9cd", background:"#fff", color:"#2d4a2d", cursor:"pointer", flex:"1 1 240px", minWidth:0, maxWidth:"100%" }}>
                      {/* Name the METRIC, not just an emoji. A planting map and a
                          yield map look alike at a glance and mean opposite things. */}
                      {mapCombos.map(c => <option key={c.key} value={c.key}>{`${c.metric==="yield"?"Harvest":"Planting"} · ${c.crop && c.crop !== "?" ? fmCropName(c.crop) : "Unknown crop"} ${c.year||""} · ${jdShowUnit(jdComboUnitLabel(c))} · ${c.n} field${c.n===1?"":"s"}`}</option>)}
                    </select>
                  ) : <span style={{ fontSize:11.5, color:"#8a5a00" }}>No built maps match the filters.</span>
                )}
                <span style={{ fontSize:11, color:"#677167" }}>
                  {fields.length} field{fields.length===1?"":"s"} · crop/year filters up top
                  {mapBounds && !isPortal ? ` · ${mapBounds.features.length} JD boundar${mapBounds.features.length===1?"y":"ies"}` : ""}
                </span>
              </div>
              {/* PHONE: map first, the field list under it, and NO inner scroller — the sheet is the
                  one scroll. The old 190px list above a 58vh map left a phone a strip of names
                  and a map that ate every swipe. */}
              <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap:0, height: isMobile ? "auto" : "58vh", minHeight: isMobile ? 0 : 360, border:"1px solid #eef2ee", borderRadius:8, overflow:"hidden" }}>
                <div style={{ order: isMobile ? 2 : 0, width: isMobile ? "auto" : 210, maxHeight: "none", flexShrink:0, overflow:"auto", borderRight: isMobile ? "none" : "1px solid #eef2ee", borderBottom: isMobile ? "1px solid #eef2ee" : "none", background:"#fafcfa" }}>
                  {mapErr && <div style={{ padding:14, color:"#c0392b", fontSize:12.5 }}>Couldn't load: {mapErr}</div>}
                  {!mapGrids && !mapErr && <div style={{ padding:16, color:"#60735f", fontSize:12.5 }}>Loading maps…</div>}
                  {mapGrids && !fields.length && <div style={{ padding:16, color:"#60735f", fontSize:12.5, lineHeight:1.5 }}>{mapGrids.length ? "No maps match the filters." : "No maps built for this farm yet — they generate over the first day after connecting."}</div>}
                  {fields.map(f => {
                    const on = f.key === mapField;
                    // The pass this row opens — the same pick every other way in uses.
                    const latest = jdPickFieldPass(f.ops, f.ops[0] && f.ops[0].field_id, null, null).hit || f.ops[0];
                    const name = dispName(f);
                    const sub = (f.ftf_label && f.ftf_label !== name) ? f.ftf_label : null;
                    return (
                      <div key={f.key} onClick={()=>{ setOpenFieldName(null); setMapField(f.key); if(latest) setMapSel(latest.op_id); if (isMobile && mapTopRef.current) mapTopRef.current.scrollIntoView({ behavior:"smooth", block:"start" }); }}
                        style={{ padding:"8px 11px", cursor:"pointer", borderBottom:"1px solid #f0f4f0", background: on?"#eaf3e6":"transparent" }}>
                        <div style={{ fontWeight: on?800:700, fontSize:13.5, color:"#1a2e1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name || "Field"}</div>
                        {sub && <div style={{ fontSize:10.5, fontWeight:600, color:"#60735f", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginTop:1 }}>{sub}</div>}
                        <div style={{ fontSize:11, color:"#60735f", marginTop:1 }}>{latest?`${latest.crop ? fmCropName(latest.crop) : "Unknown crop"} ${latest.year||""}`:""}{f.ops.length>1?` · ${f.ops.length} passes`:""}{latest&&latest.custom_from?` · ${latest.custom_role?`custom ${latest.custom_role} by `:"custom work by "}${latest.custom_from}`:""}</div>
                      </div>
                    );
                  })}
                </div>
                <div ref={mapTopRef} style={{ order: isMobile ? 1 : 0, flex:1, minWidth:0, display:"flex", flexDirection:"column", minHeight: 0, scrollMarginTop: 8 }}>
                  <div style={{ flex: isMobile ? "none" : 1, position:"relative", height: isMobile ? "46vh" : "auto", minHeight: isMobile ? 260 : 0 }}>
                    <div ref={mapDivRef} style={{ position:"absolute", inset:0 }} />
                    {/* Value under the cursor. Follows the pointer because the
                        question it answers is "what did THIS spot do" — the field
                        average is the small line underneath, for context. */}
                    {mapHover && (
                      // ⚠ A PHONE PINS IT TOP-RIGHT. Top-left is where Leaflet puts the + and −
                      // buttons, which draw above zIndex 600, so the tapped spot's yield sat
                      // hidden under them (Kevin, 2026-10-31).
                      <div style={{ position:"absolute", ...(isMobile ? { right:8, top:8 } : { left: mapHover.x+14, top: mapHover.y+14 }), zIndex:1000, pointerEvents:"none",
                                    background:"rgba(26,46,26,0.93)", color:"#fff", padding: isMobile ? "6px 11px" : "4px 9px", borderRadius:8,
                                    fontSize: isMobile ? 16 : 13, fontWeight:800, boxShadow:"0 2px 8px rgba(0,0,0,0.35)", whiteSpace:"nowrap", lineHeight:1.3 }}>
                        {mapHover.unit === "" ? mapHover.v.toLocaleString() : mapHover.v}{mapHover.unit}
                        <span style={{ display:"block", fontSize: isMobile ? 11.5 : 10, fontWeight:600, opacity:0.85 }}>
                          {mapHover.field || "Field"}{mapHover.mean!=null?` · field avg ${mapHover.mean}`:""}
                        </span>
                      </div>
                    )}
                    {/* One scale control for both modes — but the numbers behind it
                        differ: a single field scales to ITSELF (2–98% of its own
                        cells), compare scales to every field in the combo at once,
                        which is the whole point of comparing. The ramp shown is the
                        one actually being drawn. */}
                    {mapAuto && (mapMode==="compare" ? (mapCmp && mapCmp.grids.length>0) : !!curGrid) && (() => {
                      const cmpOn = mapMode==="compare";
                      const dLo = autoLo, dHi = autoHi;
                      const ramp = cmpOn ? jdOpsColor : jdYieldColor;
                      const stops = cmpOn ? [0,0.14,0.28,0.42,0.57,0.71,0.85,1] : [0,0.2,0.4,0.6,0.8,1];
                      return (
                      <div style={{ position:"absolute", left:8, bottom:8, zIndex:500, background:"rgba(255,255,255,0.95)", border:"1px solid #d9e4d9", borderRadius:8, padding:"6px 9px", fontSize:11, color:"#1a2e1a", boxShadow:"0 2px 8px rgba(0,0,0,.15)", display:"flex", gap:5, alignItems:"center", flexWrap:"wrap", maxWidth:"calc(100% - 16px)", boxSizing:"border-box" }}>
                        <span style={{ fontWeight:700 }}>Scale</span>
                        <input type="number" value={(mapScale&&mapScale.lo!=null)?mapScale.lo:dLo} onChange={e=>setMapScale(s=>({ hi:(s&&s.hi!=null)?s.hi:dHi, lo:e.target.value===""?null:Number(e.target.value) }))}
                          style={{ width: isMobile ? 72 : 54, fontSize:11, padding:"3px 5px", borderRadius:5, border:"1px solid #cdd9cd" }} />
                        <span style={{ display:"inline-flex", height:12, borderRadius:2, overflow:"hidden", border:"1px solid #2d4a2d33" }}>
                          {stops.map((t,i)=><span key={i} style={{ width:10, height:12, background:ramp(t,0,1), display:"inline-block" }} />)}
                        </span>
                        <input type="number" value={(mapScale&&mapScale.hi!=null)?mapScale.hi:dHi} onChange={e=>setMapScale(s=>({ lo:(s&&s.lo!=null)?s.lo:dLo, hi:e.target.value===""?null:Number(e.target.value) }))}
                          style={{ width: isMobile ? 72 : 54, fontSize:11, padding:"3px 5px", borderRadius:5, border:"1px solid #cdd9cd" }} />
                        {mapScale && <button onClick={()=>setMapScale(null)} title="reset to auto (2–98%)" style={{ cursor:"pointer", border:"1px solid #cdd9cd", borderRadius:5, background:"#f6faf6", padding:"2px 6px", fontSize:10, color:"#2d4a2d" }}>reset</button>}
                      </div>
                      );
                    })()}
                  </div>
                  {mapMode==="compare" && mapCmp && (
                    <div style={{ borderTop:"1px solid #e4ece4", background:"#fff", padding:"9px 12px", display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                      <div style={{ fontWeight:800, fontSize:14, color:"#1a2e1a" }}>
                        {mapCmp.combo.metric==="yield"?"Harvest":"Planting"} · {mapCmp.combo.crop && mapCmp.combo.crop !== "?" ? fmCropName(mapCmp.combo.crop) : "Unknown crop"} {mapCmp.combo.year||""} · {jdShowUnit(jdComboUnitLabel(mapCmp.combo))} · {mapCmp.grids.length} field{mapCmp.grids.length===1?"":"s"}
                        <span style={{ fontWeight:600, color:"#60735f" }}> · one shared scale</span>
                      </div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {mapCmp.mean!=null && (
                          <span style={{ fontSize:11, color:"#3a5a3a", background:"#eef4ee", border:"1px solid #dce7dc", borderRadius:5, padding:"3px 8px" }}
                            title="Acre-weighted average of the field averages">
                            operation avg {mapCmp.mean}{mapCmp.combo.unit?` ${jdShowUnit(mapCmp.combo.unit)}`:""}
                          </span>
                        )}
                        {mapCmp.meanDm!=null && (
                          <span style={{ fontSize:11, color:"#3a5a3a", background:"#eef4ee", border:"1px solid #dce7dc", borderRadius:5, padding:"3px 8px" }}
                            title="The chopper's own dry-matter figure, before moisture">
                            DM {mapCmp.meanDm} ton/ac
                          </span>
                        )}
                        {mapAuto && (
                          <span style={{ fontSize:11, color:"#3a5a3a", background:"#eef4ee", border:"1px solid #dce7dc", borderRadius:5, padding:"3px 8px" }}
                            title="2nd–98th percentile of the SMOOTHED surface, every field weighted equally">
                            ramp {mapAuto.lo} – {mapAuto.hi}
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize:11, color:"#60735f" }}>
                        {mapCmp.combo.metric==="seed_rate"
                          // Not a hedge — the fact. A planter runs a commanded rate,
                          // so flat fields ARE the map. Without this the view reads
                          // as a failed render of a yield map.
                          ? "A planting map is the commanded seeding rate — one flat colour per field is the rate, not missing detail. Pick a Harvest map from the list above for sub-field variation."
                          : (isMobile ? "Tap the map for the value at that spot — that field's own average is the small line beneath it." : "Hover the map for the value under the cursor — that field's own average is the small line beneath it.")}
                      </span>
                    </div>
                  )}
                  {mapMode!=="compare" && curGrid && (
                    <div style={{ borderTop:"1px solid #e4ece4", background:"#fff", padding:"9px 12px", display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                      {(() => {
                        const nm = jdFieldLabel(curGrid);
                        const sb = (curGrid.ftf_label && curGrid.ftf_label !== nm) ? curGrid.ftf_label : null;
                        return (
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontWeight:800, fontSize:14, color:"#1a2e1a" }}>{nm || "Field"}{curGrid.county?` · ${curGrid.county}`:""}{curGrid.custom_from?<span style={{ fontWeight:600, color:"#60735f" }}> · custom {curGrid.custom_role || "work"} by {curGrid.custom_from}</span>:null}</div>
                            {sb && <div style={{ fontSize:11, fontWeight:600, color:"#60735f", marginTop:1 }}>{sb}</div>}
                          </div>
                        );
                      })()}
                      {curOps.length>1
                        // THE LABEL IS THE FIX. Unlabelled, this control reads as a
                        // caption of what you are looking at rather than as the switch
                        // between this field's passes — and it is the only way to get
                        // from the planting map to the harvest map. Counting them says
                        // there is more than one before you open it.
                        ? <label style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:6, minWidth:0, maxWidth:"100%" }}>
                            <span style={{ fontSize:10.5, fontWeight:800, letterSpacing:.5, textTransform:"uppercase", color:"#60735f", whiteSpace:"nowrap" }}>
                              Pass ({curOps.length})
                            </span>
                            <select value={mapSel} onChange={e=>setMapSel(e.target.value)}
                              title="Switch between this field's planting and harvest maps"
                              style={{ fontSize:11.5, fontWeight:700, padding:"5px 8px", borderRadius:6, border:"1.5px solid #2d4a2d", background:"#f2f6f2", color:"#2d4a2d", cursor:"pointer", flex:"1 1 200px", minWidth:0, maxWidth:"100%" }}>
                              {curOps.map(o => <option key={o.op_id} value={o.op_id}>{opLabel(o)}</option>)}
                            </select>
                          </label>
                        : <div style={{ fontSize:12, fontWeight:700, color:"#2d4a2d", overflowWrap:"anywhere" }}>{opLabel(curGrid)}</div>}
                      {(() => {
                        const chips = [];
                        // The pass's yield in its own chip, so a phone that cuts the picker
                        // short still shows the figure and its unit (Kevin, 2026-11-01).
                        if (curGrid.metric !== "coverage" && curGrid.mean != null) chips.push(`Avg ${fmt(curGrid.mean, curGrid.mean >= 1000 ? 0 : 1)}${gUnit(curGrid)}`);
                        if (curGrid.mean_dm != null) chips.push(`DM ${fmt(curGrid.mean_dm, 1)} ton/ac`);
                        if (curGrid.date) chips.push(`${curGrid.metric==="yield"?"Harvested":"Planted"} ${curGrid.date}${curGrid.date_end&&curGrid.date_end!==curGrid.date?`–${curGrid.date_end}`:""}`);
                        if (curGrid.moisture!=null && curGrid.moisture > 0) chips.push(`Moisture ${curGrid.moisture.toFixed(1)}%`);
                        if (curGrid.variety && !/^-+$/.test(String(curGrid.variety).trim())) chips.push(`Variety ${curGrid.variety}`);
                        if (curGrid.area!=null) chips.push(`${curGrid.area.toFixed(1)} ac`);
                        return chips.length ? (
                          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                            {chips.map((c,i)=><span key={i} style={{ fontSize:11, color:"#3a5a3a", background:"#eef4ee", border:"1px solid #dce7dc", borderRadius:5, padding:"3px 8px", whiteSpace:"nowrap" }}>{c}</span>)}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}
          {data && view==="field" && (() => {
            // ⚠ BUG-058 — CORN IS ONE INSURED CROP WITH TWO MACHINE NAMES. John Deere calls a
            // chopped harvest CORN_SILAGE, so on a dairy picking CORN showed every planting with
            // a blank harvest while the harvest sat under a different option (Red Dawn 2025: 72
            // plantings, 51 chops, 39 fields on both lists). CORN now shows its silage rows
            // too; CORN SILAGE still shows only silage. Safe for the totals below because the
            // footer already keeps tons and bushels in separate sums.
            // The field name is the way to its map. A row with no `jd_field_id` cannot be
            // opened — the FSA-keyed rows built from `fsn`/`field_no` have a synthetic id
            // that no grid carries — so those stay plain text rather than offering a
            // link that would land on nothing.
            const fieldLink = (r) => (!r.jd_field_id ? r.field : (
              <span onClick={() => openFieldOnMap(r.jd_field_id, r.crop, r.year, r.field)}
                    title={`Open ${r.field} on the map — ${r.crop ? fmCropName(r.crop) : ""} ${r.year || ""}`.trim()}
                    style={{ cursor: "pointer", color: "#1a4a7a", fontWeight: 700, textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>
                {r.field}
              </span>
            ));
            const fieldCropOk = (c) => cropOk(c) || (cropFilter === "CORN" && c === "CORN SILAGE");
            // The typed search narrows on the FIELD NAME only — the column a reader is
            // scanning when they reach for it. Case- and space-insensitive so "k500"
            // finds "K 500", which is how these get typed.
            const qn = fieldQ.trim().toLowerCase().replace(/\s+/g, "");
            const nameHit = (r) => !qn || String(r.field || "").toLowerCase().replace(/\s+/g, "").includes(qn);
            const rows = sorted(fieldRows.filter(r => fieldCropOk(r.crop) && yearOk(r.year) && dateOkField(r) && nameHit(r)));
            // By field is a THIRD display path and it was reading r.yield raw while the area
            // and comparison tables were already calibrated — the same number appearing two
            // ways on one screen, which is worse than not calibrating at all. Everything that
            // shows a yield goes through calY(); there is no such thing as "just this table".
            const fy = (r) => calY(r.yield, r.crop, r.year, "yield");
            // ⚠ THE AVERAGE DIVIDES BY THE ACRES IT COUNTED BUSHELS ON, and by no
            // others. `tHarv` used to sum every row's harv_ac while `tBu` only added
            // where a yield existed, so any row carrying acres without a yield
            // silently halved the answer — Sullivan's CY2026 wheat read 42.5 bu/ac
            // against a true ~82. The row-splitting that produced those acre-only
            // rows is fixed at the source, and this is the second guard: a mean whose
            // denominator can include acres its numerator ignored is wrong by
            // construction, whatever produces the rows.
            //
            // tHarv is still the honest ACREAGE total for the column — a planted
            // field with no harvest map has real acres and they belong in the column
            // sum. It is only the AVERAGE that must not use them.
            let tPlant=0, tHarv=0, tBu=0, tYieldAc=0;
            // ⚠ TONS AND BUSHELS NEVER SHARE A TOTAL. A chopper reports MASS, so a
            // forage row carries ton/ac while the combine crops carry bu/ac — and
            // Lismore's Steuben corn already read 86.5 from (154 bu + 18.9 ton)/2 on
            // $2.28M of liability. Each unit gets its own production and its own
            // denominator, and the footer prints whichever are present.
            const byUnit = {};
            for (const r of rows) {
              tPlant += r.plant_ac||0; tHarv += r.harv_ac||0;
              // A May-July CORN harvest is a forage cut filed under Corn — never corn grain.
              // Its acres stay in the column (the row is on screen), its yield never does.
              if (jdCornHarvestMisfiled(r)) continue;
              if (r.harv_ac>0 && r.yield!=null) {
                const u = jdYieldUnitLabel(r.crop, r.yield_unit);
                const a = (byUnit[u] = byUnit[u] || { prod: 0, ac: 0 });
                a.prod += r.harv_ac*fy(r); a.ac += r.harv_ac;
                tBu += (u === "bu/ac") ? r.harv_ac*fy(r) : 0;
                tYieldAc += r.harv_ac;
              }
            }
            const unitRows = Object.keys(byUnit).sort().map(u => ({
              unit: u, prod: byUnit[u].prod, ac: byUnit[u].ac,
              avg: byUnit[u].ac > 0 ? +(byUnit[u].prod/byUnit[u].ac).toFixed(1) : null,
            }));
            // ── ONE AVERAGE PER CROP, NEVER PER UNIT ALONE ─────────────────────────
            // Kevin, 2026-10-31, on Lawnel 2: "someone who has silage and grain yield data,
            // we need to be able to easily see operation wide yields for both". The Corn
            // chip carries CORN SILAGE too, and a unit-only split printed silage's
            // 22.1 ton/ac under the label "Corn" with no grain line at all. Keyed by crop
            // AND unit, each line names its crop; a crop in view with no harvest map yet
            // is named as such rather than left out, so a missing grain map is SAID.
            const byCrop = {}, cropsSeen = [];
            for (const r of rows) {
              if (jdCornHarvestMisfiled(r)) continue;
              if (r.crop && !cropsSeen.includes(r.crop)) cropsSeen.push(r.crop);
              if (r.harv_ac>0 && r.yield!=null) {
                const u = jdYieldUnitLabel(r.crop, r.yield_unit);
                const a = (byCrop[`${r.crop}|${u}`] ||= { crop: r.crop, unit: u, prod: 0, ac: 0, dmProd: 0, dmAc: 0 });
                a.prod += r.harv_ac*fy(r); a.ac += r.harv_ac;
                // Dry matter beside the wet tons, where the pass carried a moisture.
                if (r.yield_dm != null) { a.dmProd += r.harv_ac*r.yield_dm; a.dmAc += r.harv_ac; }
              }
            }
            const hasSilage = cropsSeen.includes("CORN SILAGE");
            const cropLabel = (c) => c === "CORN" ? (hasSilage ? "Corn grain" : "Corn") : c === "CORN SILAGE" ? "Corn silage" : fmCropName(c);
            const cropRows = Object.values(byCrop)
              .map(a => ({ ...a, label: cropLabel(a.crop), avg: a.ac > 0 ? +(a.prod/a.ac).toFixed(1) : null,
                           dm: a.dmAc > 0 && a.dmAc >= a.ac - 0.05 ? +(a.dmProd/a.dmAc).toFixed(1) : null }))
              .sort((a, b) => b.ac - a.ac);
            // Only a crop the chip itself asks for is named as "no harvest maps yet" — on
            // All crops that would list every planted cover crop.
            const cropsUnmapped = cropFilter ? cropsSeen.filter(c => !cropRows.some(x => x.crop === c)) : [];
            const avgY = tYieldAc>0 ? +(tBu/tYieldAc).toFixed(1) : null;
            // ⚠ THIS AVERAGE COVERS THE ACRES A MACHINE MAPPED, WHICH IS NOT THE FARM.
            // Kevin, on Sullivan Farms: "it says he yielded 42.5 bushels per acre, I know
            // for a fact that's wrong ... not sure if it's just dividing bushels over acres
            // and he had a custom combiner help him so he's missing yield maps or what".
            // The 42.5 was a real defect and is fixed; the SECOND half of his question was
            // never answerable from this page, because it never said how much of the crop
            // it was looking at. Sullivan's CY2026 wheat is 407.9 mapped acres against
            // 482.7 insured — 85% — and the 74.8 acres it cannot see are the ground a
            // custom combine took, which yielded well below the rest.
            //
            // Measured over the 17 farms with CY2026 wheat machine data: coverage runs
            // 58% (Corcoran) to 149% (Austic). Over 100% is not an error — it is custom
            // work done for others, or headland overlap — so the figure is REPORTED and
            // never clamped, and no cause is stated for it, because this page cannot
            // source one.
            //
            // The insured denominator is policy_fields, already in the payload as
            // fsa.byFtf, under the SAME crop/year filter the rows use. A farm with no
            // insured acreage on file says so rather than showing a ratio against zero.
            const insAc = ((data.fsa && data.fsa.byFtf) || [])
              .filter(r => cropOk(r.crop) && yearOk(r.year))
              .reduce((a, r) => a + (Number(r.acres) || 0), 0);
            // ⚠ SILAGE IS INSURED AS CORN, so a Corn silage chip finds no insured acres of
            // its own and read "No insured acreage is on file" on every farm. It says why.
            const silageOnly = cropFilter === "CORN SILAGE";
            const coverPct = insAc > 0 && tYieldAc > 0 ? Math.round(100 * tYieldAc / insAc) : null;
            const ft = FOOT;
            // Records that hold nothing are left off the list, and counted rather than
            // dropped in silence.
            const emptyHere = fieldRowsAll.filter(r => jdFieldRowEmpty(r) && fieldCropOk(r.crop) && yearOk(r.year)).length;
            const emptyNote = emptyHere > 0 ? (
              <div style={{ padding:"8px 4px 0", fontSize:11.5, color:"#677167", lineHeight:1.5 }}>
                {isPortal
                  ? <>{emptyHere} machine record{emptyHere === 1 ? "" : "s"} with no acres and no yield {emptyHere === 1 ? "isn't" : "aren't"} shown.</>
                  : <>{emptyHere} John Deere record{emptyHere === 1 ? "" : "s"} with no planted or harvested acres and no yield (a zero the monitor wrote with nothing behind it) {emptyHere === 1 ? "is" : "are"} not listed.</>}
              </div>
            ) : null;
            // ⚠ AN EMPTY TABLE NAMES THE FILTER THAT EMPTIED IT. A mistyped field name
            // otherwise reads as a farm with no machine data for the crop — the one
            // conclusion this page must never reach by accident, and the same rule the
            // Coverage Book Scan and the outstanding-premium pane already carry.
            if (!rows.length && fieldQ.trim()) return (
              <div style={{ padding:"22px 10px", fontSize:12.5, color:"#5a6a5a" }}>
                No field matches <b>“{fieldQ.trim()}”</b>
                {cropFilter ? <> in {fmCropName(cropFilter)}</> : null}{yearFilter ? <> {yearFilter}</> : null}.
                {" "}<button onClick={() => setFieldQ("")}
                  style={{ border:"none", background:"none", padding:0, cursor:"pointer", color:"#2d6a2d", fontWeight:800, fontSize:12.5, textDecoration:"underline" }}>
                  Clear the search</button> to read the whole table.
              </div>
            );
            // ── THE PHONE GETS CARDS, NOT NINE COLUMNS ─────────────────────────────
            // Kevin, 2026-10-31: the table ran off a 390px screen with no cue and the
            // dates broke onto two lines. Same rows, same calibration (fy), same
            // acre-coverage figure as the caption below — one card per field-year, and
            // the card IS the way to that field's map when it has one.
            if (isMobile) {
              const harvestOp = metric !== "seed_rate";
              const md = (d) => { const t = d ? String(d).slice(0, 10) : ""; if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
                                  return new Date(t + "T12:00:00").toLocaleDateString("en-US", { month:"short", day:"numeric" }); };
              const list = [...rows].sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0)
                || String(a.field || "").localeCompare(String(b.field || ""), undefined, { numeric:true }));
              const shown = list.slice(0, cardCap);
              const lbl = { fontSize:10.5, fontWeight:800, letterSpacing:".07em", textTransform:"uppercase", color:"#60735f" };
              return (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {!rows.length && (
                    <div style={{ padding:"18px 4px", fontSize:13, color:"#5a6a5a", lineHeight:1.5 }}>
                      No field records for {cropFilter ? fmCropName(cropFilter) : "any crop"}{yearFilter ? ` in ${yearFilter}` : ""}{dateActive ? " in that date window" : ""}.
                      {" "}Try another year or crop above.
                    </div>
                  )}
                  {rows.length > 0 && (
                    <div style={{ border:"1px solid #e3ebe3", borderRadius:12, padding:"10px 12px", background:"#fbfdfb", display:"flex", flexDirection:"column", gap:4 }}>
                      {harvestOp ? (
                        // NEVER AVERAGED ACROSS CROPS. Corn at 200 and wheat at 80 share
                        // a unit and nothing else; the average waits for one crop.
                        !cropFilter ? (
                          <>
                            <span style={lbl}>{yearFilter || "All years"} · harvested</span>
                            <span style={{ fontSize:20, fontWeight:800, fontVariantNumeric:"tabular-nums" }}>{tHarv > 0 ? tHarv.toFixed(1) : "0"} <span style={{ fontSize:12, color:"#60735f" }}>ac</span></span>
                            {cropRows.length > 0 ? (
                              <>
                                <span style={{ ...lbl, marginTop:6 }}>Mapped average by crop</span>
                                {cropRows.slice(0, 8).map(c => (
                                  <span key={c.crop + c.unit} style={{ display:"flex", justifyContent:"space-between", gap:10, fontSize:13.5 }}>
                                    <span style={{ fontWeight:700, color:"#2d3f2d" }}>{c.label}</span>
                                    <span style={{ fontVariantNumeric:"tabular-nums" }}><b>{c.avg}</b> <span style={{ fontSize:11, color:"#60735f" }}>{jdShowUnit(c.unit)}{c.dm != null ? ` · ${c.dm} DM` : ""} · {c.ac.toFixed(1)} ac</span></span>
                                  </span>
                                ))}
                              </>
                            ) : <span style={{ fontSize:11.5, color:"#5a6a5a" }}>No harvest maps for {yearFilter || "these years"} yet.</span>}
                          </>
                        ) : cropRows.length ? (
                          <>
                            <span style={lbl}>{fmCropName(cropFilter)}{yearFilter ? ` ${yearFilter}` : ""} · mapped average</span>
                            {cropRows.map(c => (
                              <span key={c.crop + c.unit} style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:10 }}>
                                {cropRows.length + cropsUnmapped.length > 1 && <span style={{ fontSize:13.5, fontWeight:700, color:"#2d3f2d" }}>{c.label}</span>}
                                <span style={{ fontSize:22, fontWeight:800, fontVariantNumeric:"tabular-nums" }}>{c.avg} <span style={{ fontSize:12, color:"#60735f" }}>{jdShowUnit(c.unit)}{c.dm != null ? ` · ${c.dm} DM` : ""} · {c.ac.toFixed(1)} ac</span></span>
                              </span>
                            ))}
                            {cropsUnmapped.map(c => (
                              <span key={c} style={{ display:"flex", justifyContent:"space-between", gap:10, fontSize:13 }}>
                                <span style={{ fontWeight:700, color:"#2d3f2d" }}>{cropLabel(c)}</span>
                                <span style={{ color:"#60735f" }}>no harvest maps yet{yearFilter ? ` for ${yearFilter}` : ""}</span>
                              </span>
                            ))}
                            {coverPct != null && !silageOnly && (
                              <span style={{ height:6, borderRadius:3, background:"#e3ebe3", overflow:"hidden", display:"block" }}>
                                <span style={{ display:"block", height:"100%", width:`${Math.min(100, coverPct)}%`, background:"#6fa84f" }} />
                              </span>
                            )}
                            <span style={{ fontSize:11.5, color:"#5a6a5a", lineHeight:1.45 }}>
                              {silageOnly
                                ? <>{tYieldAc.toFixed(1)} acres mapped. Silage is insured as Corn, so insured acres aren't split between grain and silage.</>
                                : insAc > 0
                                ? <>{tYieldAc.toFixed(1)} of {insAc.toFixed(1)} insured acres have a yield map ({coverPct}%). Acres without a map aren't in the average.</>
                                : <>{tYieldAc.toFixed(1)} acres mapped. No insured acreage is on file for this crop and year to compare against.</>}
                            </span>
                            {calAny && calFor(cropFilter, yearFilter) && (
                              <span style={{ fontSize:11, color:"#2d5a10", fontWeight:700 }}>Post-calibrated ×{calFor(cropFilter, yearFilter).factor} to the entered total</span>
                            )}
                          </>
                        ) : (
                          <span style={{ fontSize:12.5, color:"#5a6a5a" }}>No harvest maps for {fmCropName(cropFilter)}{yearFilter ? ` ${yearFilter}` : ""} yet.</span>
                        )
                      ) : (
                        <>
                          <span style={lbl}>{cropFilter ? fmCropName(cropFilter) : "All crops"}{yearFilter ? ` ${yearFilter}` : ""} · planted</span>
                          <span style={{ fontSize:20, fontWeight:800, fontVariantNumeric:"tabular-nums" }}>{tPlant > 0 ? tPlant.toFixed(1) : "0"} <span style={{ fontSize:12, color:"#60735f" }}>ac</span></span>
                        </>
                      )}
                    </div>
                  )}
                  {shown.length > 0 && (
                    <div style={{ border:"1px solid #e3ebe3", borderRadius:12, overflow:"hidden", background:"#fff" }}>
                      {shown.map((r, i) => {
                        const canOpen = !!r.jd_field_id;
                        // In words, never John Deere's codes: "Corn, chopped for silage", not
                        // "CORN → CORN SILAGE".
                        const crop = jdCornHarvestMisfiled(r) ? "Corn (a July cut, not grain)"
                          : r.planted_as && r.planted_as !== r.crop
                          ? (r.planted_as === "CORN" && r.crop === "CORN SILAGE" ? "Corn, chopped for silage" : `${fmCropName(r.planted_as)} → ${fmCropName(r.crop)}`)
                          : cropLabel(r.crop);
                        const when = harvestOp
                          ? (r.harv_date ? `harvested ${md(r.harv_date) || r.harv_date}` : r.plant_date ? `planted ${md(r.plant_date) || r.plant_date}` : null)
                          : (r.plant_date ? `planted ${md(r.plant_date) || r.plant_date}` : null);
                        const ac = harvestOp ? (r.harv_ac > 0 ? r.harv_ac : null) : (r.plant_ac > 0 ? r.plant_ac : null);
                        const yv = r.yield != null ? fy(r) : null;
                        return (
                          <div key={i} onClick={canOpen ? () => openFieldOnMap(r.jd_field_id, r.crop, r.year, r.field) : undefined}
                            style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:10, alignItems:"center", padding:"10px 12px",
                                     borderTop: i ? "1px solid #eef3ee" : "none", cursor: canOpen ? "pointer" : "default" }}>
                            <div style={{ minWidth:0 }}>
                              <div style={{ fontSize:14, fontWeight:800, color: canOpen ? "#1a4a7a" : "#1a2e1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.field || "Field"}</div>
                              <div style={{ fontSize:11.5, color:"#60735f", marginTop:1 }}>
                                <span style={{ color:CROP_COLOR[r.crop] || "#2d3f2d", fontWeight:700 }}>{crop}</span> {r.year}
                                {when ? ` · ${when}` : ""}{ac != null ? ` · ${Number(ac).toFixed(1)} ac` : ""}
                              </div>
                            </div>
                            <div style={{ textAlign:"right", display:"flex", alignItems:"center", gap:8 }}>
                              {harvestOp ? (
                                yv != null
                                  ? <span style={{ fontSize:15, fontWeight:800, fontVariantNumeric:"tabular-nums" }}>{yv.toFixed(1)}<span style={{ display:"block", fontSize:10, fontWeight:600, color:"#60735f" }}>{jdShowUnit(jdYieldUnitLabel(r.crop, r.yield_unit))}{r.yield_dm != null ? ` · ${r.yield_dm} DM` : ""}</span></span>
                                  : <span style={{ fontSize:11, fontWeight:700, color: r.harv_ac > 0 ? "#9a5a1a" : "#8a978a" }}>{r.harv_ac > 0 ? "no yield map" : r.harv_date ? "no acres recorded" : "not harvested"}</span>
                              ) : null}
                              {canOpen && <span style={{ color:"#9fb09f", fontSize:16 }}>›</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {emptyNote}
                  {list.length > shown.length && (
                    <button onClick={() => setCardCap(c => c + 150)}
                      style={{ border:"1px solid #cdd9cd", borderRadius:9, padding:"10px", background:"#f6faf6", color:"#2d4a2d", fontWeight:800, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                      Show more ({list.length - shown.length} left)
                    </button>
                  )}
                </div>
              );
            }
            return (
            <>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>{th("Field","field")}{th("Year","year")}{th("Crop","crop")}{th("Planted","plant_date")}{th("Plant ac","plant_ac",1)}{th("Harvested","harv_date")}{th("Harv ac","harv_ac",1)}{th("Yield","yield",1)}{th("Production","prod",1)}</tr></thead>
              <tbody>{rows.map((r,i) => (
                <tr key={i}>{td(fieldLink(r))}{td(r.year)}{td(jdCornHarvestMisfiled(r) ? <span title="John Deere filed this harvest as Corn in summer, when no corn is combined for grain — a forage cut with the monitor left on Corn. It is not counted as corn grain.">{cropTag(r.crop)}<span style={{ display:"block", fontSize:10, color:"#9a5a1a", fontWeight:700 }}>summer cut, not grain</span></span> : r.planted_as ? <span title={`John Deere recorded the planting as ${fmCropName(r.planted_as)} and the harvest as ${fmCropName(r.crop)} — one field, shown on one row`}>{cropTag(r.crop)}</span> : r.relabel_twin ? <span title={`John Deere holds this planting under both ${fmCropName(r.relabel_twin)} and ${fmCropName(r.crop)} — same field, date and acres — so it is shown once`}>{cropTag(r.crop)}</span> : cropTag(r.crop))}{td(r.plant_date||"—")}{td(r.plant_ac!=null?r.plant_ac.toFixed(1):"—",{textAlign:"right"})}{td((r.harv_date && r.unmeasured_harvests) ? <span title={`John Deere also holds harvest records dated ${r.unmeasured_harvests.join(", ")} that carry no area, no yield and no map`}>{r.harv_date}</span> : r.harv_date || (r.unmeasured_harvests ? <span title={`John Deere holds harvest records dated ${r.unmeasured_harvests.join(", ")} that carry no area, no yield and no map. Whether a harvest happened that day is not established from the record.`} style={{ color:"#677167" }}>{r.unmeasured_harvests[0]} <span style={{ fontSize:10 }}>{r.unmeasured_harvests.length > 1 ? `· ${r.unmeasured_harvests.length} records, ` : "· "}no area or yield recorded</span></span> : "—"))}{td(r.harv_ac!=null?r.harv_ac.toFixed(1):"—",{textAlign:"right"})}{td(r.yield!=null?<>{Math.round(fy(r)*10)/10}<span style={{ fontSize:10, color:"#60735f", fontWeight:600 }}> {jdProdWord(jdYieldUnitOrNull(r.crop, r.yield_unit)) || "unit not recorded"}</span>{r.yield_bu!=null && <span title={`the same yield in bushels. Dry beans are insured in POUNDS per acre — the APH on this book is around 1,690 lb/ac — while John Deere and Ops Center report bushels. 60 lb/bu.`} style={{ fontSize:10, color:"#5a7a5a", fontWeight:600 }}> · {r.yield_bu} bu</span>}{r.yield_dm!=null && <span title={`dry matter at ${r.moisture_pct}% moisture — the figure Ops Center calls Yield`} style={{ fontSize:10, color:"#5a7a5a", fontWeight:600 }}> · {r.yield_dm} DM</span>}</>: r.yield_refused ? <span title={`${r.yield_refused.reason}${r.yield_refused.moisture_pct!=null?` John Deere also recorded ${r.yield_refused.moisture_pct}% moisture on this pass — grain comes off at 15–25%.`:""}`} style={{ color:"#677167", fontWeight:600, fontSize:11 }}>{r.yield_refused.recorded}<span style={{ fontSize:10 }}> recorded · not a usable yield</span></span> : "—",{textAlign:"right",fontWeight:700})}{td((r.harv_ac>0&&r.yield!=null&&!jdCornHarvestMisfiled(r))?Math.round(r.harv_ac*fy(r)).toLocaleString():"—",{textAlign:"right",fontWeight:800,color:"#1a4a1a"})}</tr>
              ))}</tbody>
              {/* A calibrated figure must never appear unlabelled, on ANY tab. */}
              {rows.length>0 && calAny && (() => {
                const shown = [...new Set(rows.map(r => `${String(r.crop||"").toUpperCase()}|${r.year}`))]
                  .map(k => data.calibration[k]).filter(Boolean);
                if (!shown.length) return null;
                return <caption style={{ captionSide:"top", textAlign:"left", fontSize:11, color:"#2d5a10", fontWeight:700, paddingBottom:4 }}>
                  Post-calibrated ×{shown.map(c => c.factor).join(", ×")} — yields scaled to the entered total
                </caption>;
              })()}
              {rows.length>0 && tYieldAc>0 && (
                <caption style={{ captionSide:"bottom", textAlign:"left", fontSize:11, color:"#5a6a5a", paddingTop:6, lineHeight:1.5 }}>
                  {silageOnly
                    ? <>Yield averages the <b>{tYieldAc.toFixed(1)} acres a machine mapped</b>. Silage is insured as Corn, so insured acres are not split between grain and silage.</>
                    : insAc > 0
                    ? <>Yield averages the <b>{tYieldAc.toFixed(1)} acres a machine mapped</b>, against <b>{insAc.toFixed(1)} insured acres</b> on file for this crop and year — <b>{coverPct}%</b>. {coverPct < 95 ? "Acres with no yield map are not in this average." : coverPct > 105 ? "Mapped acres exceed the insured acreage on this policy." : ""}</>
                    : <>Yield averages the <b>{tYieldAc.toFixed(1)} acres a machine mapped</b>. No insured acreage is on file for this crop and year to compare against.</>}
                </caption>
              )}
              {rows.length>0 && <tfoot><tr>{td("TOTAL",ft)}{td("",ft)}{td("",ft)}{td("",ft)}{td(tPlant>0?tPlant.toFixed(1):"—",{...ft,textAlign:"right"})}{td("",ft)}{td(tHarv>0?tHarv.toFixed(1):"—",{...ft,textAlign:"right"})}{td(cropRows.length?cropRows.map(u => (cropRows.length>1?u.label+" ":"")+(u.avg==null?"—":u.avg+" "+jdShowUnit(u.unit))).join(" · "):"—",{...ft,textAlign:"right"})}{td(cropRows.length?cropRows.map(u => (cropRows.length>1?u.label+" ":"")+(jdProdWord(u.unit)?Math.round(u.prod).toLocaleString()+" "+jdProdWord(u.unit):"—")).join(" · "):"—",{...ft,textAlign:"right",color:"#1a4a1a"})}</tr></tfoot>}
            </table>
            {emptyNote}
            </>
            );
          })()}
          {data && view==="area" && (() => {
            // ONE SOURCE FOR EVERY GROUPING. This used to read byCounty for "county" and
            // byFtf for everything else — two different row sets behind one GROUP button,
            // so pressing it changed the TOTAL (Panek Farms 2025 corn: 4,850.5 ac by
            // County, 3,524.8 by Unit). byFtf is now the complete pool, county included,
            // and areaKey does the county grouping — so the total cannot move.
            // ⚠ attributed === false is ground on NO insured CLU. It is not this farm's,
            // it is not another insured's, and it does not belong on a policy page — see
            // the note above this view. Filtered at the source so no total, footer or
            // later addition can pick it up.
            const src = (data.machine.byFtf||[]).filter(r => r.attributed !== false && r.metric===metric && cropOk(r.crop) && yearOk(r.year) && dateOk(r));
            // ── COMPLETENESS ────────────────────────────────────────────────────────
            // Every row below is derived from a BUILT YIELD MAP, so an operation that
            // has not been processed is not flagged — it is ABSENT, and the table reads
            // as though those acres do not exist. Gary Swede Farms' CY2026 wheat read
            // "34.7 harvested acres" on 2026-09-03 with five of his six harvests
            // unprocessed and 287 acres in his own Ops Center.
            //
            // ⚠ THE OLD WARNING EXISTED AND FIRED ONLY ON AN EMPTY TABLE (`if
            // (!src.length)` below), so the FIRST map to land silenced it and left the
            // partial number standing alone. Partial is the normal state of this page
            // while a farm is converging, not the exceptional one — so the banner is
            // driven by what is PENDING, never by whether any row happened to render.
            //
            // Scoped to the crop and year, never the county: an unmapped operation has
            // no county, because the county comes out of the grid.
            const pend = (data.machine.coverage || [])
              .filter(r => r.op === (metric === "yield" ? "harvest" : "seeding") && cropOk(r.crop) && yearOk(r.year));
            const pSum = (k) => pend.reduce((s, r) => s + (Number(r[k]) || 0), 0);
            const pendOps = pSum("pending_ops"), expAc = pSum("acres"), pendAc = pSum("pending_acres");
            const pendWhy = [
              [pSum("not_queued"), "not yet queued for a map"],
              [pSum("queued"), "building now"],
              [pSum("errored"), "failed to build"],
              [pSum("awaiting_split"), "mapped, awaiting the county/CLU split"],
            ].filter(([n]) => n > 0).map(([n, w]) => `${n} ${w}`).join(" · ");
            if (!src.length) return (() => {
              // "No planting machine data" is FALSE whenever By field is showing
              // rows, and saying it sends you hunting a parser bug. By field reads
              // jd_field_operations directly; every By-area rollup is derived from
              // BUILT YIELD MAPS, so a farm whose grids haven't generated yet has
              // hundreds of operations and an empty rollup. Kennedy Farms: 432
              // operations, 4 grids built — By field listed 1,204 ac of 2026 corn
              // planting while By area said there was none.
              //
              // So count what's actually waiting and say that instead.
              // Sourced from the same `pend` rows the PARTIAL banner uses. It used to
              // count byField rows with acres, which is a SECOND definition of "waiting"
              // and drifted from the banner the moment either changed.
              const waiting = pendOps, acres = pendAc;
              const what = metric === "yield" ? "harvest" : "planting";
              // ⚠ A FARMER GETS ONE PLAIN LINE. The agent's version names admin pages a
              // grower cannot open (Connected Accounts, Build maps), and "(0.0 ac)" reads
              // as a broken number — Kevin, previewing Amos Smith, 2026-10-31. Records
              // John Deere holds with no acres on them are called exactly that.
              const scope = `${cropFilter ? fmCropName(cropFilter) + " " : ""}${yearFilter ? yearFilter + " " : ""}`;
              const noAc = !(acres > 0.05);
              const recs = noAc
                ? `${waiting} ${what} record${waiting===1?"":"s"} with no acres yet`
                : `${waiting} ${what} operation${waiting===1?"":"s"} (${acres.toFixed(1)} ac)`;
              return (
                <div style={{ padding:20, color:"#60735f", fontSize:13, lineHeight:1.55 }}>
                  {waiting > 0 && isPortal ? (
                    <>
                      <b style={{ color:"#1a2e1a" }}>Your {scope}{what} maps aren't ready yet.</b>
                      <br />They'll show here once they're processed. <b>By field</b> shows what the machine has recorded so far.
                    </>
                  ) : waiting > 0 ? (
                    <>
                      <b style={{ color:"#8a5a00" }}>{recs}: in this farm's data but not in By area yet.</b>
                      <br />By area is built from the machine <b>maps</b>, not the operation list — each one has to generate before it can be split by county and CLU. <b>By field</b> shows them all now.
                      <br /><span style={{ fontSize:12 }}>To pull them in now: <b>Connected Accounts</b> → this farm's row → <b>Build maps</b>, then <b>Recompute maps</b> to attribute them by county and CLU. Otherwise the background job works through them over the next few days.</span>
                    </>
                  ) : `No ${scope}${what} machine data${scope ? "" : " for this crop and year"}.`}
                  {/* This used to offer "County rollups are ready now" when a non-county
                      grouping came up empty. Every grouping now reads the same pool, so
                      an empty Unit view means an empty County view — the offer would send
                      you to a tab with nothing on it. */}
                </div>
              );
            })();
            const agg = {};
            // ⚠ ONE ROW PER UNIT, NEVER A MEAN ACROSS TWO. Lawnel 2's 2026 alfalfa is 61
            // harvester-mass maps at 1.4 DM ton/ac and 21 with no unit at 40.2; keyed on crop
            // alone they averaged to 11.9 and took the one label in the set. The unit is
            // resolved first (a stamped bu/ac on forage or an unknown crop is no unit at all),
            // and a row with none is its own row.
            for (const r of src) { const a=areaKey(r,groupBy); const u=metric==="yield"?(jdYieldUnitOrNull(r.crop, r.yield_unit)||""):""; const base=`${a.key}|${r.crop}|${r.year}`; const k=`${base}|${u}`; const o=(agg[k] ||= { base, area:a.label, crop:r.crop, year:r.year, unmatched:!!a.unmatched, acres:0, wsum:0, dac:0, dsum:0, units:new Set() }); o.acres += r.acres; o.wsum += (calY(r.mean,r.crop,r.year,metric)||0)*r.acres; if (r.mean_dm!=null) { o.dac += r.acres; o.dsum += (calY(r.mean_dm,r.crop,r.year,metric)||0)*r.acres; } if (u) o.units.add(u); }
            // An area-crop split into two unit rows has ONE insured acreage: it goes on the
            // larger row, and neither gets a reporting basis — the entered total cannot be
            // divided between a ton figure and an unknown one.
            const perBase = {};
            for (const k in agg) { const o = agg[k]; (perBase[o.base] ||= []).push(o); }
            for (const b in perBase) perBase[b].sort((x, y) => y.acres - x.acres);
            // ── PRODUCTION REPORTING BASIS: INSURED ACRES, NOT THE COMBINE'S FOOTPRINT ──
            // A production report is filed against the acres RMA insured. The monitor covers
            // whatever it covered — Kennedy's wheat is 388.8 machine acres against 389.8
            // reported — so reporting on the footprint quietly under-reports the gap, and
            // reporting the monitor's raw bushels ignores that the combine was never
            // calibrated.
            //
            // So: the monitor decides the SPLIT and the grower's own total decides the LEVEL.
            // Each unit's share is (its yield x its insured acres) over the sum of that, and
            // the entered total is divided along it. The parts then sum to the whole exactly,
            // which a production report has to do — a per-unit rescale on footprint acres does
            // not, because it leaves the unmonitored acres out of the total.
            const insuredBy = {};
            if (metric === "yield") for (const r of ((groupBy==="county" ? data.fsa.byCounty : data.fsa.byFtf) || [])) {
              if (!cropOk(r.crop) || !yearOk(r.year)) continue;
              const a = areaKey(r, groupBy);
              insuredBy[`${a.key}|${r.crop}|${r.year}`] = (insuredBy[`${a.key}|${r.crop}|${r.year}`] || 0) + (Number(r.acres) || 0);
            }
            // Weight sums per crop+year, so each entered total is split only across its own crop.
            const shareTot = {};
            for (const k in agg) {
              const o = agg[k], ins = perBase[o.base].length === 1 ? (insuredBy[o.base] || 0) : 0;
              if (!(o.acres > 0) || !(ins > 0)) continue;
              const cy = `${String(o.crop||"").toUpperCase()}|${o.year}`;
              if (!calFor(o.crop, o.year)) continue;
              shareTot[cy] = (shareTot[cy] || 0) + (o.wsum / o.acres) * ins;
            }
            for (const k in agg) {
              const o = agg[k]; o.insured = perBase[o.base][0] === o ? (insuredBy[o.base] || null) : null;
              const c = calFor(o.crop, o.year), cy = `${String(o.crop||"").toUpperCase()}|${o.year}`;
              if (!c || perBase[o.base].length > 1 || !(o.insured > 0) || !(o.acres > 0) || !(shareTot[cy] > 0)) continue;
              const share = ((o.wsum / o.acres) * o.insured) / shareTot[cy];
              o.repProd  = c.total_bushels * share;
              o.repYield = Math.round((o.repProd / o.insured) * 10) / 10;
            }
            const rows = sorted(Object.values(agg).map(o => ({ area:o.area, crop:o.crop, year:o.year, unmatched:o.unmatched, acres:+o.acres.toFixed(1), yield_unit:o.units.size===1?[...o.units][0]:(o.units.size?"mixed":null), mean:o.acres>0?+(o.wsum/o.acres).toFixed(1):null, meanDm:o.acres>0&&Math.abs(o.dac-o.acres)<0.01?+(o.dsum/o.acres).toFixed(1):null, insured:o.insured!=null?+(+o.insured).toFixed(1):null, repYield:o.repYield!=null?o.repYield:null, repProd:o.repProd!=null?o.repProd:null })));
            // Only widen the table when there is a reporting basis to show.
            const anyRep = rows.some(r => r.repProd != null);
            const isY = metric==="yield";
            let tAc=0, tIns=0;
            const cropTot = {};
            // ⚠ ACRES ON NO INSURED CLU ARE NOT THIS FARM'S ACRES, SO THEY ARE NOT IN ITS
            // TOTAL. The rows above already separate them and say so; the footer used to
            // put them back, which is how Cody Bentley's card read "124.4 harvested" on a
            // farm with 102.3 insured. The acres are real — they are a custom harvester's
            // work on ground nobody in this book insures — and a total that includes them
            // invites exactly the question Kevin asked.
            //
            // Counted and NAMED below the table, never dropped: 60 acres of real harvest
            // vanishing without a word is the same defect from the other side.
            let tUnAc = 0, tUnRows = 0;
            const tUnProd = {};
            for (const r of rows) {
              if (r.unmatched) {
                tUnAc += r.acres||0; tUnRows++;
                if (isY && r.mean!=null && r.yield_unit) tUnProd[r.yield_unit] = (tUnProd[r.yield_unit]||0) + r.acres*r.mean;
                continue;
              }
              tAc += r.acres||0;
              tIns += r.insured||0;
              // ⚠ NEVER A TOTAL ACROSS CROPS. Corn, soybeans and wheat are all bu/ac, so a
              // one-unit test let the TOTAL row print one average over all three. Each crop
              // (and each unit within it) keeps its own yield and production, the rule the
              // By field footer follows (cropRows).
              if (isY && r.mean!=null) {
                const u = jdYieldUnitLabel(r.crop, r.yield_unit);
                const e = (cropTot[`${r.crop}|${u}`] ||= { crop:r.crop, unit:u, ac:0, prod:0, ins:0, rep:0 });
                e.ac += r.acres||0; e.prod += r.acres*r.mean; e.ins += r.insured||0; e.rep += r.repProd||0;
              }
            }
            const tUnNote = Object.entries(tUnProd).filter(([,v])=>v>0)
              .map(([u,v])=>`${Math.round(v).toLocaleString()} ${jdProdWord(u)}`).join(" + ");
            // The footer reports on the SAME basis as the rows. A total that sums the
            // footprint while its rows report on insured acres is the contradiction that
            // makes an agent stop trusting the page.
            const totRows = Object.values(cropTot).sort((a, b) => b.ac - a.ac);
            const totSilage = totRows.some(e => e.crop === "CORN SILAGE");
            const totLabel = (e) => (e.crop === "CORN" && totSilage ? "Corn grain" : fmCropName(e.crop))
              + (totRows.filter(x => x.crop === e.crop).length > 1 ? ` (${jdShowUnit(e.unit)})` : "");
            // One crop prints its figure; several print a line each, named.
            const totCell = (fmt) => {
              const xs = totRows.map(e => ({ e, v: fmt(e) })).filter(x => x.v != null);
              if (!xs.length) return "—";
              if (totRows.length === 1) return xs[0].v;
              return <span>{xs.map(({ e, v }, j) => <span key={j} style={{ display:"block", fontSize:11.5 }}>{totLabel(e)} {v}</span>)}</span>;
            };
            // Units differ by crop — silage is TONS, grain is BUSHELS. The header used
            // to hardcode "bu/ac", which after the silage correction would have
            // labelled 25 ton/ac as 25 bu/ac. Per-row units, and the TOTAL only
            // aggregates when every row in view shares one unit; mixing bushels and
            // tons into a single sum would be a meaningless number.
            const ft = FOOT;
            // Acres this farm's machine ran on ANOTHER insured's ground are removed from
            // every figure above — they belong on that owner's report. A correct removal
            // nobody can see is the same defect as an incorrect one, so it is stated.
            const custom = (data.machine.customWork || [])
              .filter(r => r.metric===metric && cropOk(r.crop) && yearOk(r.year))
              .reduce((s, r) => s + (Number(r.acres)||0), 0);
            // Replanted ground is counted ONCE (the endpoint keeps whichever pass covered
            // more of the field and drops the other). This is what was dropped — stated,
            // because acres quietly disappearing is the same defect as acres quietly
            // doubling.
            const replant = (data.machine.replantWork || [])
              .filter(r => r.metric===metric && cropOk(r.crop) && yearOk(r.year))
              .reduce((s, r) => s + (Number(r.acres)||0), 0);
            // Forage is cut several times and each cutting writes its own map, so the
            // endpoint counts the ground once. This is the difference, stated for the
            // same reason the replant line is: a total that quietly shrank is as bad as
            // one that quietly doubled.
            const cutRows = (data.machine.cuttingWork || [])
              .filter(r => r.metric===metric && cropOk(r.crop) && yearOk(r.year));
            const cutAc = cutRows.reduce((s, r) => s + (Number(r.acres)||0), 0);
            const cutFields = cutRows.reduce((s, r) => s + (Number(r.fields)||0), 0);
            const cutMax = cutRows.reduce((m, r) => Math.max(m, Number(r.max_events)||0), 0);
            return (
              <>
              {/* A grower gets one plain line; the build steps are agency tools they can't reach. */}
              {pendOps > 0 && isPortal && (
                <div style={{ margin:"0 0 10px", padding:"9px 12px", background:"#fff8e6", border:"1px solid #e8c97a", borderRadius:8, fontSize:13, lineHeight:1.5, color:"#6b4a00" }}>
                  {pendAc > 0.05
                    ? <>{pendAc.toFixed(1)} more acres of {isY ? "harvest" : "planting"} are still being mapped and will show here when they're ready.</>
                    : <>A few more {isY ? "harvest" : "planting"} records are still being processed and will show here when they're ready.</>}
                </div>
              )}
              {pendOps > 0 && !isPortal && (
                <div style={{ margin:"0 0 10px", padding:"9px 12px", background:"#fff8e6",
                              border:"1px solid #e8c97a", borderRadius:8, fontSize:12.5,
                              lineHeight:1.55, color:"#6b4a00" }}>
                  <b>PARTIAL — {pendOps} {isY ? "harvest" : "planting"} operation{pendOps === 1 ? "" : "s"} still being mapped.</b>{" "}
                  {pendAc > 0.05
                    ? <>{pendOps === 1 ? "It covers" : "They cover"} {pendAc.toFixed(1)} ac of this farm&rsquo;s John Deere data and</>
                    : <>John Deere has recorded no acres on {pendOps === 1 ? "it" : "them"} yet, and {pendOps === 1 ? "it" : "they"}</>}{" "}
                  {pendOps === 1 ? "has" : "have"} no yield map yet, so {pendOps === 1 ? "it is" : "they are"} not
                  in the table below. Those acres are <b>missing, not zero</b>.
                  {pendWhy && <div style={{ marginTop:4, fontSize:11.5, color:"#7a6330" }}>{pendWhy}.</div>}
                  <div style={{ marginTop:4, fontSize:11.5 }}>
                    To pull them in now: <b>Connected Accounts</b> → this farm&rsquo;s row → <b>Build maps</b>,
                    then <b>Recompute maps</b> to split them by county and CLU. Otherwise the background job works through them.
                  </div>
                </div>
              )}
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                {/* Planting shows acres only. A seeding "rate" column cannot be summed
                    or compared across the book — a drilled bean field is in lb/ac and a
                    planted one in seeds/ac — and this table exists to support acreage
                    reporting, where the rate has no bearing. Harvest keeps yield and
                    production, which are the insurable figures. */}
                <thead><tr>{th(AREA_DIMS.find(d=>d[0]===groupBy)[1],"area")}{th("Crop","crop")}{th("Year","year")}{th(isY?"Harvested ac":"Planted ac","acres",1)}{isY && anyRep && th("Insured ac","insured",1)}{isY && th("Yield","mean",1)}{isY && anyRep && th("Report yield","repYield",1)}{isY && th("Production","prod",1)}</tr></thead>
                <tbody>{rows.map((r,i) => (
                  <tr key={i}>{td(r.area)}{td(cropTag(r.crop))}{td(r.year)}{td(r.acres!=null?r.acres.toFixed(1):"—",{textAlign:"right"})}
                    {isY && anyRep && td(r.insured>0?r.insured.toFixed(1):"—",{textAlign:"right"})}
                    {isY && td(r.mean!=null?<>{`${r.mean} ${jdShowUnit(jdYieldUnitLabel(r.crop, r.yield_unit))}`}{r.meanDm!=null && <div style={{ fontSize:10.5, fontWeight:500, color:"#60735f" }}>DM {r.meanDm} ton/ac</div>}</>:"—",{textAlign:"right",fontWeight:700})}
                    {/* On INSURED acres — what actually gets filed. Bold because on a unit row
                        this is the number that goes on the production report. */}
                    {isY && anyRep && td(r.repYield!=null?`${r.repYield} ${jdShowUnit(jdYieldUnitLabel(r.crop, r.yield_unit))}`:"—",{textAlign:"right",fontWeight:800,color:"#1f5c2e"})}
                    {/* The production word comes from the row's OWN unit. Read off the crop
                        name it printed "bu" beside alfalfa and triticale tonnage. */}
                    {isY && td(!jdProdWord(jdYieldUnitLabel(r.crop, r.yield_unit)) ? "—" : r.repProd!=null
                        ? `${Math.round(r.repProd).toLocaleString()} ${jdProdWord(jdYieldUnitLabel(r.crop, r.yield_unit))}`
                        : ((r.acres>0&&r.mean!=null)?`${Math.round(r.acres*r.mean).toLocaleString()} ${jdProdWord(jdYieldUnitLabel(r.crop, r.yield_unit))}`:"—"),
                      {textAlign:"right",fontWeight:800,color:"#1a4a1a"})}</tr>
                ))}</tbody>
                {rows.length>0 && <tfoot><tr>{td(pendOps>0?"TOTAL (partial)":"TOTAL",ft)}{td("",ft)}{td("",ft)}{td(tAc>0?tAc.toFixed(1):"—",{...ft,textAlign:"right"})}
                  {isY && anyRep && td(tIns>0?tIns.toFixed(1):"—",{...ft,textAlign:"right"})}
                  {isY && td(totCell(e => (e.ac > 0 && e.prod > 0) ? `${+(e.prod/e.ac).toFixed(1)} ${jdShowUnit(e.unit)}` : null),{...ft,textAlign:"right"})}
                  {isY && anyRep && td(totCell(e => (e.ins > 0 && e.rep > 0) ? `${+(e.rep/e.ins).toFixed(1)} ${jdShowUnit(e.unit)}` : null),{...ft,textAlign:"right",color:"#1f5c2e"})}
                  {isY && td(totCell(e => !jdProdWord(e.unit) ? null : e.rep > 0 ? `${Math.round(e.rep).toLocaleString()} ${jdProdWord(e.unit)}`
                            : (e.prod > 0 ? `${Math.round(e.prod).toLocaleString()} ${jdProdWord(e.unit)}` : null)),
                     {...ft,textAlign:"right",color:"#1a4a1a"})}</tr></tfoot>}
              </table>
              {/* ⚠ COUNTED AND NAMED, NEVER DROPPED — the same rule as the sibling table.
                  These acres are real machine work on ground no policy in this book covers,
                  so they are not this farm's to report; but 60 acres vanishing from a total
                  without a word is the same defect from the other side. */}
              {/* BACKSTOP, NOT A FEATURE. The source filter above means no unmatched row
                  reaches this view, so tUnAc is always 0 and this never renders. It is kept
                  deliberately: if that filter is ever removed, the acres must not silently
                  re-enter a total unexplained — which is the defect this page carried twice
                  in one day. Defense in depth, the same shape as assertUniformKeys sitting
                  inside replaceRows. */}
              {tUnAc > 0 && (
                <div style={{ padding:"8px 10px 0", fontSize:11.5, color:"#7a6a3a", lineHeight:1.5 }}>
                  Not counted above: <b>{tUnAc.toFixed(1)} ac</b>{tUnRows > 1 ? ` across ${tUnRows} rows` : ""}{isY && tUnNote ? ` (${tUnNote})` : ""} this farm&rsquo;s machine {isY?"harvested":"planted"} on
                  ground that is <b>not an insured field of this farm</b>. There is no policy acreage behind it, so it is outside the total&nbsp;&mdash;
                  which is why the total can never exceed this farm&rsquo;s own insured acres.
                </div>
              )}
              {custom > 0 && (
                <div style={{ padding:"8px 10px 0", fontSize:11.5, color:"#7a6a3a", lineHeight:1.5 }}>
                  Not counted above: <b>{custom.toFixed(1)} ac</b> this farm's machine {isY?"harvested":"planted"} on
                  another insured's ground. Those acres sit on that owner's report, not this one.
                </div>
              )}
              {replant > 0 && (
                <div style={{ padding:"8px 10px 0", fontSize:11.5, color:"#8a5a00", lineHeight:1.5 }}>
                  <b>Replant counted once.</b> {replant.toFixed(1)} ac of a second seeding pass sits on ground already
                  in the total above, so it is not added again — the pass covering more of each field is the one kept.
                </div>
              )}
              {cutAc > 0 && (
                <div style={{ padding:"8px 10px 0", fontSize:11.5, color:"#8a5a00", lineHeight:1.5 }}>
                  <b>Each cutting counted once.</b> {cutFields} field{cutFields===1?"":"s"} {cutFields===1?"was":"were"} harvested
                  more than once{cutMax>1?` (up to ${cutMax} cuttings)`:""}, so {cutAc.toFixed(1)} ac of repeat cutting sits on ground
                  already in the total above and is not added again. Yield is the SEASON total per acre, so every cutting&rsquo;s
                  tonnage is still counted.
                </div>
              )}
              </>
            );
          })()}
          {data && view==="compare" && (() => {
            // Machine side: ONE pool for every grouping, same as the rollup table above —
            // the Machine-vs-FSA comparison has to be reading the acres the rollup shows.
            // Same rule as the By-area view above: ground on no insured CLU is not policy
            // data and never reaches this comparison.
            const mSrc = (data.machine.byFtf||[]).filter(r => r.attributed !== false && r.metric===metric && cropOk(r.crop) && yearOk(r.year) && dateOk(r));
            // FSA side: for the COUNTY grouping use crop_coverage (byCounty = the RMA
            // insured/physical county), which aligns with the now-physical machine
            // county — a farm insured across several counties compares county-for-
            // county. For FSN/Tract/Field use byFtf (policy_fields carries those FSA
            // identifiers; crop_coverage has no FSN/tract). Previously this always used
            // byFtf's ADMIN county, which lumped every county's keyed acres onto the
            // operation's single admin county (all of Corcoran's wheat under Genesee).
            const fSrc = (groupBy==="county" ? (data.fsa.byCounty||[]) : (data.fsa.byFtf||[])).filter(r => cropOk(r.crop) && yearOk(r.year));
            // ⚠ BOTH SIDES KEY ON THE INSURED CROP (BUG-062). RMA insures corn silage
            // as CORN with type_code 026 against grain's 016, so John Deere's "CORN
            // SILAGE" has no FSA row of its own — it keyed against nothing, and the
            // chopped acres showed as a residual equal to their whole acreage while
            // the insured CORN row showed only the grain half. Measured 2026-09-11:
            // 4,638 CY2026 chopped acres reconciled against nothing (Bridgewater
            // WILLIAMS 2,633 ac against 3,827 insured; Lawnel LIVINGSTON 2,005 ac
            // against 1,924). `jdInsuredCropKey` is the same rule `insuredCropKey`
            // applies server-side in Claim Watch.
            //
            // ⚠ ACREAGE FOLDS; UNITS DO NOT. Grain is bu/ac and a chopper's grid is
            // dry-matter tons, so the folded row keeps its yields PER UNIT and the
            // operational names that made it up. Blending them is the Lismore defect
            // — 86.5 from (154 bu + 18.9 ton)/2 on $2.28M of liability.
            const mAgg={}; for (const r of mSrc){ const a=areaKey(r,groupBy); const ck=jdInsuredCropKey(r.crop); const k=`${a.key}|${ck}|${r.year}`; const o=(mAgg[k] ||= { area:a.label, crop:ck, year:r.year, unmatched:!!a.unmatched, ac:0, units:new Set(), parts:{}, perUnit:{} }); o.ac += r.acres; const u=jdYieldUnitOrNull(r.crop, r.yield_unit); if (u) o.units.add(u); o.parts[r.crop]=(o.parts[r.crop]||0)+r.acres; const pu=(o.perUnit[u||"unit not recorded"] ||= { ac:0, wsum:0 }); pu.ac += r.acres; pu.wsum += (calY(r.mean,r.crop,r.year,metric)||0)*r.acres; }
            const fAgg={}; for (const r of fSrc){ const a=areaKey(r,groupBy); const ck=jdInsuredCropKey(r.crop); const k=`${a.key}|${ck}|${r.year}`; const o=(fAgg[k] ||= { area:a.label, crop:ck, year:r.year, unmatched:!!a.unmatched, ac:0, pp:0, guar:0 }); o.ac += r.acres; o.pp += (r.pp_acres||0); o.guar += (r.guar_bu||0); }
            // Residual closes the loop: machine-planted + prevent-plant should equal
            // insured acres. Without PP a wet year reads as missing machine data
            // rather than unplanted ground.
            const rows = [...new Set([...Object.keys(mAgg),...Object.keys(fAgg)])].map(k => { const mo=mAgg[k], fo=fAgg[k], src=mo||fo; const m=mo?mo.ac:0, f=fo?fo.ac:0, pp=fo?fo.pp:0;
              // An uninsured crop has no policy to reconcile against, so it gets no
              // residual at all rather than one equal to its whole machine acreage.
              const unins = jdCropUninsured(src.crop);
              // ⚠ ACRES ON NO INSURED CLU HAVE NO FSA SIDE, SO THEY GET NO RATIO AND NO
              // RESIDUAL — the same treatment an uninsured crop already gets, for the same
              // reason. Cody Bentley custom-harvests: 61.0 of his 128.6 soybean acres sit on
              // ground nobody in our book insures, and dividing them by HIS planted acres
              // produced "122% harvested" on a farm that was 63% done. A residual computed
              // against zero would read +61.0, which says "61 acres unreported" about acres
              // that are not his to report.
              const unmatched = !!src.unmatched;
              // No FSA row at all for this (area, crop, year) — we hold no acreage report
              // for it, so there is nothing to reconcile against. NOT the same as an FSA
              // row whose planted figure is zero, which is a real comparison.
              const noFsa = !fo;
              // FSA PLANTED = insured acres − PP (crop_coverage.acres is planted+PP; PP
              // is the prevent-plant subset). Compare machine planted/harvested against
              // planted acres; PP is shown in its own column, never in this base.
              const fplant = +(f - pp).toFixed(1);
              return { area:src.area, crop:src.crop, year:src.year, uninsured:unins,
                       unmatched, noFsa,
                       machine:+m.toFixed(1), fsa:fplant, pp:+pp.toFixed(1),
                       diff:+(m-fplant).toFixed(1), resid: (unins || unmatched || noFsa) ? null : +(m-fplant).toFixed(1),
                       guar: fo ? Math.round(fo.guar||0) : 0,
                       pctHarv: (fplant>0 && !unmatched) ? Math.round(m/fplant*100) : null,
                       // The operational names behind a folded row, largest first —
                       // "CORN · silage 2,633 + grain 3,921" rather than a bare CORN
                       // that hides which half is which.
                       parts: mo ? Object.entries(mo.parts).sort((a,b)=>b[1]-a[1]).map(([c,ac])=>({ crop:c, ac:+ac.toFixed(1) })) : [],
                       // One entry per unit. A row carrying two of them prints both and
                       // no combined figure, because there is no number that is the
                       // average of a bushel and a ton.
                       yields: mo ? Object.entries(mo.perUnit).filter(([,v])=>v.ac>0).map(([u,v])=>({ unit:u, ac:+v.ac.toFixed(1), mean:+(v.wsum/v.ac).toFixed(1) })).sort((a,b)=>b.ac-a.ac) : [],
                       myunit: mo ? (mo.units.size===1?[...mo.units][0]:(mo.units.size?"mixed":null)) : null,
                       // ⚠ NULL WHEN THE ROW HOLDS MORE THAN ONE UNIT. A single mean
                       // over grain bushels and silage tons is neither quantity.
                       myield:(mo&&mo.ac>0&&mo.units.size<=1)?+(Object.values(mo.perUnit).reduce((t,v)=>t+v.wsum,0)/mo.ac).toFixed(1):null }; });
            if (!rows.length) return <div style={{ padding:20, color:"#60735f", fontSize:13, lineHeight:1.5 }}>{groupBy!=="county" && ftfEmpty
              ? (isPortal ? "Not ready yet: your maps are still being matched to your FSA fields. The County view works now." : "Farm-Tract-Field attribution is still computing (machine grids matched to FSA CLUs). County comparison is ready now.")
              : (isPortal ? "Nothing to compare yet for this crop and year." : "No overlapping data to compare yet.")}</div>;
            const isY = metric==="yield";
            const srt = sorted(rows);
            // Totals exclude uninsured crops from the acre columns AND the residual —
            // otherwise 85 acres of green beans inflate "machine acres" against an FSA
            // side that was never going to include them.
            let tM=0, tF=0, tBu=0, tPP=0, tGuar=0;
            // ⚠ ONLY BUSHELS REACH THE BUSHEL TOTAL (BUG-062). This summed
            // `machine x myield` on every row with no unit test, so a farm holding
            // grain in bu/ac and a chopper's grid in DM ton/ac had its tons added
            // straight into "Production" and into the average below it. Measured
            // 2026-09-11: 43 of 172 farm-years hold more than one yield unit —
            // Gary Swede and Lawnel in every recent season. The per-unit totals are
            // accumulated separately and printed separately.
            const tByUnit = {};
            // ⚠⚠ THE TOTAL MIXED TWO POPULATIONS AND THAT IS WHY IT COULD READ OVER 100%.
            // `tM` summed every machine acre including the "not matched to a CLU" row, while
            // `tF` took 0 from that row because there is no insured acreage behind it — a
            // numerator and denominator drawn from different populations, which is the
            // mixed-ratio class this file already records three times (the claim ring at 98%,
            // fsnCoverage at 102%, the guarantee priced two ways).
            //
            // Kevin, 2026-10-08: *"It should not be possible for us to be showing more acres
            // harvested than insured because yield data is supposed to be attributed to
            // insured CLU."* Correct, and it now cannot: the ratio is matched acres over
            // planted acres, and the unmatched acres are stated on their own line under the
            // table rather than being deleted. Cody Bentley reads **64.3 / 102.3 = 63%**,
            // which is what Field Progress has been saying all along, with "61.0 ac not
            // matched to an insured CLU" named beside it.
            //
            // They are COUNTED, never dropped — 61 acres of real harvest that our CLU pass
            // could not place is a fact about the data worth a line, and a page that silently
            // discards it invites the next reader to go looking for missing machine data.
            let tUnmatched = 0, tUnmatchedRows = 0;
            // Production leaving with those acres, per unit — so the three buckets
            // reconcile to what the table shows and nothing has to be taken on trust.
            const tUnmatchedProd = {}, tNoFsaProd = {};
            const addProd = (acc, r) => { for (const y of (r.yields||[])) { if (!(y.ac>0) || y.mean==null) continue; acc[y.unit] = (acc[y.unit]||0) + y.ac*y.mean; } };
            const prodNote = (acc) => Object.entries(acc).filter(([,v])=>v>0)
              .map(([u,v])=>`${Math.round(v).toLocaleString()} ${String(u).replace(/\/ac$/,"")}`).join(" + ");
            // ⚠ AND MACHINE ACRES FROM A YEAR WE HOLD NO ACREAGE REPORT FOR ARE EXCLUDED
            // TOO, for the identical reason — there is no denominator. With "All years"
            // selected that was nine crop years of harvest over one year of FSA acreage:
            // Bentley's footer read 809%. Counted and named, never dropped.
            let tNoFsa = 0, tNoFsaRows = 0;
            // Guarantees by unit: a dry bean guarantee is pounds and cannot be added to corn bushels.
            const tGuarBy = {};
            for (const r of srt) {
              if (r.unmatched) { tUnmatched += r.machine||0; tUnmatchedRows++; if (isY) addProd(tUnmatchedProd, r); continue; }
              if (r.noFsa) { tNoFsa += r.machine||0; tNoFsaRows++; if (isY) addProd(tNoFsaProd, r); continue; }
              if (r.uninsured) continue; tM += r.machine||0; tF += r.fsa||0; tPP += r.pp||0; tGuar += r.guar||0; if (r.guar>0) { const gu = `${fmCropName(r.crop)} ${jdIsLbCrop(r.crop) ? "lb" : "bu"}`; tGuarBy[gu] = (tGuarBy[gu]||0) + r.guar; }
              if (isY) for (const y of (r.yields||[])) { if (!(y.ac>0) || y.mean==null) continue; const a=(tByUnit[y.unit] ||= { prod:0, ac:0 }); a.prod += y.ac*y.mean; a.ac += y.ac; } }
            tBu = (tByUnit["bu/ac"] ? tByUnit["bu/ac"].prod : 0);
            // ⚠ AND ONE LINE PER CROP, not per unit alone. Corn, soybeans and wheat are all
            // bu/ac, so a per-unit footer printed one "bu/ac" average blending all three.
            // Keyed on the insured crop and the unit: CORN in bu/ac is the grain, CORN in a
            // ton unit is the silage, each over its own acres. Same rows the TOTAL counts.
            const tByCrop = {};
            if (isY) for (const r of srt) {
              if (r.unmatched || r.noFsa || r.uninsured) continue;
              for (const y of (r.yields||[])) { if (!(y.ac>0) || y.mean==null) continue; const c=(tByCrop[`${r.crop}|${y.unit}`] ||= { crop:r.crop, unit:y.unit, prod:0, ac:0 }); c.prod += y.ac*y.mean; c.ac += y.ac; }
            }
            const tCropUnits = {};
            for (const v of Object.values(tByCrop)) tCropUnits[v.crop] = (tCropUnits[v.crop] || 0) + 1;
            const tCropLabel = (v) => (v.crop === "CORN" && tCropUnits.CORN > 1 ? (/ton/i.test(v.unit) ? "Corn silage" : /bu/i.test(v.unit) ? "Corn grain" : "Corn") : fmCropName(v.crop))
              + (v.crop !== "CORN" && tCropUnits[v.crop] > 1 ? ` (${jdShowUnit(v.unit)})` : "");
            const tUnitRows = Object.values(tByCrop).sort((a,b)=>b.ac-a.ac)
              .map(v=>({ crop:v.crop, label:tCropLabel(v), unit:v.unit, ac:+v.ac.toFixed(1), prod:Math.round(v.prod), mean:v.ac>0?+(v.prod/v.ac).toFixed(1):null }));
            const tPctHarv = tF>0 ? Math.round(tM/tF*100) : null;   // tF is already FSA PLANTED (PP excluded)
            const tDiff = +(tM-tF).toFixed(1);
            const tResid = +(tM-tF).toFixed(1);
            const residColor = (v, base) => Math.abs(v) <= Math.max(2, base*0.02) ? T.ok : (Math.abs(v) <= Math.max(5, base*0.05) ? T.warn : T.danger);
            // ⚠ THE AVERAGE USED TO DIVIDE BY EVERY MACHINE ACRE. `tBu/tM` put
            // bushels over the chopped acres too, which reads low for exactly the
            // farms this fix is about — the Sullivan 42.5 defect, one table over.
            // `tUnitRows` carries each unit's mean over its OWN acres, so there is
            // no single blended average left to compute.
            const ft = FOOT;
            return (
              <>
                {/* The farmer reads the same table in plain words; the agency's version
                    keeps the definitions it reconciles by. */}
                {isPortal ? (
                <div style={{ padding:"2px 10px 10px", fontSize:12, color:"#5a6a5a", lineHeight:1.55 }}>
                  The acres your machine {metric==="seed_rate"?"planted":"harvested"} on your insured fields, beside the acres on your acreage report.
                  {" "}<b style={{ color:T.body }}>FSA planted</b> is your insured acres less any prevented planting, which has its own column.
                  {" "}<b style={{ color:T.body }}>Difference</b> is machine acres minus FSA planted: near zero means the two agree. A bigger gap can be a field the machine didn&rsquo;t record, or a crop named differently in the two places.
                </div>
                ) : (
                <div style={{ padding:"2px 10px 10px", fontSize:11, color:"#677167", lineHeight:1.55 }}>
                  Machine {metric==="seed_rate"?"planted":"harvested"} acres vs FSA planted acres.
                  {" "}<b style={{ color:T.body }}>FSA planted = insured acres − prevent-plant</b>; PP is shown in its own column, never in this base.
                  {" "}<b style={{ color:T.body }}>% {metric==="seed_rate"?"plant":"harv"}</b> is machine acres <b style={{ color:T.body }}>matched to an insured CLU</b> over FSA planted acres — so it cannot exceed 100% by counting ground this farm does not insure.
                  {" "}The <b style={{ color:T.body }}>TOTAL row totals the comparable rows only</b> — every column from the same set — and the acres it leaves out are stated beneath the table.
                  {" "}<b style={{ color:T.body }}>Residual = Machine − FSA planted</b> — near zero means the planted acreage report is
                  accounted for. A large residual is a to-do, not a verdict: it can be unreported acres, a field the planter never
                  recorded, or a crop whose name doesn&rsquo;t match between the two systems.
                </div>
                )}
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>{th(AREA_DIMS.find(d=>d[0]===groupBy)[1],"area")}{th("Crop","crop")}{th("Year","year")}{th("Machine ac","machine",1)}{th(isPortal ? "Prevented ac" : "PP ac","pp",1)}{th("FSA planted ac","fsa",1)}{th(isY?"% harv":"% plant","pctHarv",1)}{th(isPortal ? "Difference" : "Residual","resid",1)}{isY && th("Machine yield","myield",1)}{isY && th("Production","prod",1)}{isY && th(isPortal ? "Guarantee" : "County guar","guar",1)}</tr></thead>
                  <tbody>{srt.map((r,i) => (
                    <tr key={i} style={r.uninsured ? { opacity:0.62 } : undefined}>{td(r.area)}{td(<>{cropTag(r.crop)}{r.parts.length>1 && <span title={`John Deere recorded ${r.parts.map(p=>`${fmCropName(p.crop)} ${p.ac.toFixed(1)} ac`).join(" + ")} — both are insured as ${fmCropName(r.crop)}, so the acres reconcile as one crop while the yields stay in their own units.`} style={{ display:"block", fontSize:10, color:"#60735f", fontWeight:600 }}>{r.parts.map(p=>`${/SILAGE/.test(p.crop)?"silage":"grain"} ${p.ac.toFixed(0)}`).join(" + ")}</span>}</>)}{td(r.year||"—")}{td(r.machine?r.machine.toFixed(1):"—",{textAlign:"right"})}{td(r.pp?r.pp.toFixed(1):"—",{textAlign:"right",color:r.pp>0?T.warn:undefined,fontWeight:r.pp>0?700:400})}{td(r.fsa?r.fsa.toFixed(1):"—",{textAlign:"right"})}{td(r.pctHarv!=null?`${r.pctHarv}%`:"—",{textAlign:"right",fontWeight:700,color:r.pctHarv!=null?(r.pctHarv>=90?T.ok:r.pctHarv>=60?T.warn:T.danger):undefined})}{td(r.uninsured ? <span title="John Deere reports this crop but it isn't insurable here, so there's no policy to reconcile against." style={{ fontSize:10.5, fontWeight:700, color:T.faint }}>not insured</span> : <span style={{ color: residColor(r.resid, r.fsa||0), fontWeight:800 }}>{r.resid>0?"+":""}{r.resid}</span>,{textAlign:"right"})}{isY && td(isY
                      ? (r.yields.length > 1
                          // Two units under one insured crop: print BOTH, never a mean
                          // of a bushel and a ton.
                          ? <span title="Grain and chopped acres are insured as one crop but measured in different units, so they are reported separately.">{r.yields.map((y,j)=>(
                              <span key={j} style={{ display:"block", fontSize:11.5 }}>{y.mean} <span style={{ color:"#6a7a6a", fontWeight:600 }}>{y.unit}</span></span>))}</span>
                          : (r.myield!=null ? `${r.myield} ${jdYieldUnitLabel(r.crop, r.myunit)}` : "—"))
                      : (r.myield!=null ? Math.round(r.myield).toLocaleString() : "—"),
                      {textAlign:"right",fontWeight:700,color:"#1a2e1a"})}{isY && td(
                      r.yields.length > 1
                        ? <span>{r.yields.map((y,j)=>(
                            <span key={j} style={{ display:"block", fontSize:11.5 }}>{Math.round(y.ac*y.mean).toLocaleString()} <span style={{ color:"#6a7a6a", fontWeight:600 }}>{String(y.unit).replace(/\/ac$/,"")}</span></span>))}</span>
                        : ((r.machine>0&&r.myield!=null)?`${Math.round(r.machine*r.myield).toLocaleString()} ${String(jdYieldUnitLabel(r.crop, r.myunit)).replace(/\/ac$/,"")}`:"—"),
                      {textAlign:"right",fontWeight:800,color:"#1a4a1a"})}{isY && td(r.guar>0?`${r.guar.toLocaleString()} ${jdIsLbCrop(r.crop)?"lb":"bu"}`:"—",{textAlign:"right",fontWeight:700,color:"#5a6a2d"})}</tr>
                  ))}</tbody>
                  <tfoot><tr>{td("TOTAL",ft)}{td("",ft)}{td("",ft)}{td(tM>0?tM.toFixed(1):"—",{...ft,textAlign:"right"})}{td(tPP>0?tPP.toFixed(1):"—",{...ft,textAlign:"right",color:tPP>0?T.warn:undefined})}{td(tF>0?tF.toFixed(1):"—",{...ft,textAlign:"right"})}{td(tPctHarv!=null?`${tPctHarv}%`:"—",{...ft,textAlign:"right"})}{td(<span style={{ color: residColor(tResid, tF||0) }}>{tResid>0?"+":""}{tResid}</span>,{...ft,textAlign:"right"})}{isY && td(isY
                    // ⚠ ONE LINE PER UNIT. The footer used to print a single average
                    // over every machine acre and a single "Production" that had
                    // added dry-matter tons to bushels — 43 of 172 farm-years hold
                    // both. Each unit now carries its own mean over its OWN acres.
                    ? (tUnitRows.length
                        ? <span>{tUnitRows.map((u,j)=>(
                            <span key={j} style={{ display:"block", fontSize:11.5 }}>{tUnitRows.length>1 ? `${u.label} ` : ""}{u.mean} <span style={{ color:"#6a7a6a" }}>{jdShowUnit(u.unit)}</span></span>))}</span>
                        : "—")
                    : "—",{...ft,textAlign:"right"})}{isY && td(
                    tUnitRows.length
                      ? <span>{tUnitRows.map((u,j)=>(
                          <span key={j} style={{ display:"block", fontSize:11.5 }}>{tUnitRows.length>1 ? `${u.label} ` : ""}{jdProdWord(u.unit) ? <>{u.prod.toLocaleString()} <span style={{ color:"#6a7a6a" }}>{jdProdWord(u.unit)}</span></> : "—"}</span>))}</span>
                      : "—",{...ft,textAlign:"right",color:"#1a4a1a"})}{isY && td(tGuar>0?(Object.keys(tGuarBy).length===1?Object.entries(tGuarBy).map(([u,v])=>`${Math.round(v).toLocaleString()} ${u.split(" ").pop()}`).join(""):<span>{Object.entries(tGuarBy).map(([u,v],j)=><span key={j} style={{ display:"block", fontSize:11.5 }}>{u.split(" ").slice(0,-1).join(" ")} {Math.round(v).toLocaleString()} {u.split(" ").pop()}</span>)}</span>):"—",{...ft,textAlign:"right",color:"#5a6a2d"})}</tr></tfoot>
                </table>
                {/* ⚠ COUNTED AND NAMED, NEVER DROPPED. These are real acres the machine
                    harvested that our CLU pass could not place on any insured field — on a
                    custom harvester that is the neighbour's ground, and on a farm that
                    should be fully attributed it is a to-do. Excluding them from the ratio
                    without saying so would send the next reader hunting for machine data we
                    already have, which is the opposite of the problem. */}
                {tNoFsa > 0 && (
                  <div style={{ margin:"8px 10px 0", padding:"8px 10px", border:"1px solid #d8dfe8", background:"#f3f6fa", borderRadius:6, fontSize:11.5, color:"#4a5a6a", lineHeight:1.55 }}>
                    <b>{tNoFsa.toFixed(1)} machine ac in crop years with no acreage report on file</b>{tNoFsaRows > 1 ? ` across ${tNoFsaRows} rows` : ""}{isY && prodNote(tNoFsaProd) ? ` (${prodNote(tNoFsaProd)})` : ""} — {isY ? "harvested" : "planted"}, but there is no FSA planted acreage on file for that year to compare them against.
                    {" "}Listed above, and <b>not</b> in the TOTAL, the % or the {isPortal ? "difference" : "residual"}: dividing several years of machine acres by one year of acreage would read far over 100%, and subtracting from an absent report would read as unreported acres.
                    {" "}Narrow the year filter to compare like years.
                  </div>
                )}
                {/* BACKSTOP — see the note on the By-area view. The source filter means
                    this never renders; it exists so removing that filter cannot go unnoticed. */}
                {tUnmatched > 0 && (
                  <div style={{ margin:"8px 10px 0", padding:"8px 10px", border:"1px solid #e3d9b8", background:"#fbf7e8", borderRadius:6, fontSize:11.5, color:"#6a5d2d", lineHeight:1.55 }}>
                    <b>{tUnmatched.toFixed(1)} machine ac not matched to an insured CLU</b>{tUnmatchedRows > 1 ? ` across ${tUnmatchedRows} rows` : ""}{isY && prodNote(tUnmatchedProd) ? ` (${prodNote(tUnmatchedProd)})` : ""} — harvested on ground that is <b>not an insured field of this farm</b>, so there is no policy acreage behind it.
                    {" "}Listed above, and <b>not</b> in the TOTAL, the % or the residual, because there is no insured acreage to compare them against.
                    {" "}Custom work for a non-customer looks exactly like this; so does a field whose boundary we hold under the wrong farm.
                    {" "}It is <b>not</b> a missing boundary — every insured field is checked for one on the Boundaries tab.
                  </div>
                )}
              </>
            );
          })()}
          {/* Units whose machine yield runs below the guarantee across the WHOLE unit (agents only). */}
          {view==="watch" && !isPortal && <UnitGuaranteePanel farmerId={farmerId || farmer.id} year={Number(yearFilter) || CURRENT_CROP_YEAR} fetchJson={(u) => authedFetch(u).then(r => r.json())} />}
          {view==="watch" && (() => {
            if (watchErr) return <div style={{ padding:16, color:"#c0392b", fontSize:13 }}>Couldn't load: {watchErr}</div>;
            if (!watch) return <div style={{ padding:24, color:"#60735f", fontSize:13 }}><UiSpinner />Reading harvest data…</div>;
            const areas = (watch.areas || []).filter(a => cropOk(a.crop));
            const pct = (c) => Math.round((c||0)*100);
            return (
              <div style={{ padding:"6px 10px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", padding:"10px 14px", borderRadius:10, marginBottom:12, background: watch.farm_harvest_complete?"#e5f3df":"#f2f6f2", border:`1px solid ${watch.farm_harvest_complete?"#bcdcb4":"#dbe6db"}` }}>
                  <span style={{ fontSize:14, fontWeight:800, color:"#1a2e1a" }}>{watch.farm_harvest_complete ? "Harvest complete" : "Harvest in progress"}{watch.year?` · ${watch.year}`:""}</span>
                  {watch.last_harvest_date && <span style={{ fontSize:12, color:"#60735f" }}>last activity {watch.last_harvest_date}{watch.days_since_harvest!=null?` · ${watch.days_since_harvest}d ago`:""}</span>}
                  {watch.likely_claims>0 && <span style={{ marginLeft:"auto", fontSize:12, fontWeight:800, color:"#b13a24", background:"#fde8e4", borderRadius:8, padding:"3px 10px" }}>{watch.likely_claims} likely claim{watch.likely_claims>1?"s":""}</span>}
                  {/* ⚠ NOTHING-TO-REVIEW AND NOT-CHECKED RENDER THE SAME WAY IF BOTH ARE 0.
                      `review_flags` counts areas whose unmatched acres crossed the
                      threshold; on a county-level read nothing can cross it, because there
                      is no CLU attribution to compare against. The API answers null there
                      now, and null gets a badge of its own — silence on a farm we have not
                      checked is the one outcome that reads as a clean bill of health. */}
                  {watch.review_flags>0 && <span style={{ fontSize:12, fontWeight:700, color:"#8a5a00", background:"#fff3d9", borderRadius:8, padding:"3px 10px" }}>{watch.review_flags} to review</span>}
                  {watch.review_flags==null && <span title="This farm is on the county-level read — its fields are not keyed to CLUs, so the unmatched-acre check cannot run at all. Key the acreage to turn it on." style={{ fontSize:12, fontWeight:700, color:"#6a6a7a", background:"#eef0f4", borderRadius:8, padding:"3px 10px" }}>review check not run</span>}
                </div>
                {!areas.length && <div style={{ padding:20, color:"#60735f", fontSize:13, lineHeight:1.5 }}>No keyed insured fields to watch yet{watch.year?` for ${watch.year}`:""}. Claim detection runs on CLU-keyed acres — wheat now, spring crops once acreage is finalized.</div>}
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {areas.map((a,i) => (
                    <div key={i} style={{ border:`1px solid ${a.likely_claim?"#e6b8ae":a.complete?"#cfe3ca":"#e3e9e3"}`, borderRadius:10, padding:"10px 14px", background: a.likely_claim?"#fdf3f1":"#fff" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                        <span style={{ fontWeight:800, fontSize:13.5, color:CROP_COLOR[a.crop]||"#1a2e1a" }}>{fmCropName(a.crop)}</span>
                        <span style={{ fontSize:12.5, color:"#4a6e4a" }}>{a.county}</span>
                        {a.county_level && <span title="County-level estimate — this farm's fields aren't keyed to CLUs yet, so harvest is compared to insured county acres, not field-by-field. Key the acreage to upgrade to the full field-level view." style={{ fontSize:10, fontWeight:700, color:"#7a6a3a", background:"#f4efe2", borderRadius:8, padding:"2px 7px" }}>county-level</span>}
                        {a.complete ? <span style={{ fontSize:10.5, fontWeight:800, color:"#2d6a2d", background:"#e5f3df", borderRadius:8, padding:"2px 8px" }}>{a.county_level ? "HARVESTED" : "ALL FIELDS IN"}</span>
                          : <span style={{ fontSize:10.5, fontWeight:800, color:"#8a5a00", background:"#fff3d9", borderRadius:8, padding:"2px 8px" }}>{a.fields_harvested!=null?`${a.fields_harvested}/${a.fields_total} fields`:`${pct(a.completeness)}%`}</span>}
                        {a.likely_claim && <span title="Machine-yield screener: John Deere yield against the planted-acre guarantee, before tickets exist. Not a claim estimate." style={{ marginLeft:"auto", fontSize:11.5, fontWeight:800, color:"#fff", background:"#b13a24", borderRadius:8, padding:"3px 10px" }}>LIKELY CLAIM{a.est_indemnity?` · ~$${a.est_indemnity.toLocaleString()}`:""} · machine-yield screener</span>}
                        {a.likely_claim && a.claim_partial_basis && <span title="The yield behind this read was measured on part of the ground and applied to every insured acre." style={{ fontSize: 10.5, fontWeight: 700, color: "#7a5c00", background: "#fff8e8", border: "1px solid #e8d9ae", borderRadius: 6, padding: "1px 6px", marginLeft: 6 }}>on {Math.round((a.yield_sample_share || 0) * 100)}% of the acres</span>}
                      </div>
                      <div style={{ height:7, borderRadius:4, background:"#eef2ee", margin:"8px 0", overflow:"hidden" }}>
                        <div style={{ width:`${Math.min(100,pct(a.completeness))}%`, height:7, background: a.complete?"#4a9a4a":"#d8a93a" }} />
                      </div>
                      <div style={{ display:"flex", gap:16, flexWrap:"wrap", fontSize:12, color:"#3a4f3a" }}>
                        <span>Insured <b>{a.insured_acres}</b> ac ({a.insured_clus} fld)</span>
                        <span>Harvested <b>{a.harvested_acres}</b> ac</span>
                        {a.fields_total!=null && <span>Fields <b>{a.fields_harvested}/{a.fields_total}</b> combined</span>}
                        {a.acre_coverage>0 && <span title="Machine-mapped acres vs insured acres — normally a bit under (headlands) but can run over (added / under-reported land)">Coverage <b>{Math.round(a.acre_coverage*100)}%</b></span>}
                        {a.yield!=null && <span>Yield <b style={{ color: a.likely_claim?"#b13a24":"#1a4a1a" }}>{a.yield}</b> {watchUnit(a)}</span>}
                        {a.guarantee_yield!=null && <span>Guar <b>{a.guarantee_yield}</b> {watchUnit(a)}</span>}
                        {a.silage_yield!=null && <span>Silage <b style={{ color:"#8a5a2a" }}>{a.silage_yield}</b> {silageUnitLabel(a.silage_unit)}</span>}
                        {a.silage_guarantee!=null && <span>Silage guar <b>{a.silage_guarantee}</b> ton/ac</span>}
                        {/* ⚠ 0 BUSHELS IS A CLAIM ABOUT THE CROP. The API answers null where
                            no harvest row carried a yield inside its crop bounds, so a cell
                            with harvested acres and no usable yield says so instead of
                            quietly dropping the figure beside a "Harvested 240 ac". */}
                        {a.production_bu!=null ? <span>Production <b>{a.production_bu.toLocaleString()}</b> {watchTotalUnit(a)}</span>
                          : a.harvested_acres>0 ? <span style={{ color:"#7a7a6a" }} title="Acres were harvested, but no operation carried a yield inside this crop's plausible range — so production is unknown, not zero.">Production <b>not measured</b></span>
                          : null}
                        {a.complete && a.guarantee_bu!=null && <span>Guarantee <b>{a.guarantee_bu.toLocaleString()}</b> {watchTotalUnit(a)}</span>}
                        {a.likely_claim && a.shortfall_bu>0 && <span style={{ color:"#b13a24", fontWeight:700 }}>Short {a.shortfall_bu.toLocaleString()} {watchTotalUnit(a)}</span>}
                      </div>
                      {/* ⚠ THE NAMED ABSENCE FOR BOTH CHECKS BELOW. They are gated at >= 5 ac,
                          and `null >= 5` is false — so before the API stopped answering 0,
                          a county-level card printed NEITHER warning and read as fully
                          reconciled. It is the read a farm gets before its acreage is keyed,
                          which is exactly when an agent has least ability to check it. */}
                      {(a.unmatched_acres==null || a.missing_data_acres==null) && (
                        <div style={{ marginTop:8, fontSize:11.5, color:"#5a5a6a", background:"#f4f5f8", border:"1px solid #dfe2ea", borderRadius:7, padding:"6px 10px" }}>
                          County-level read — two checks did not run: acres harvested on no insured CLU, and insured fields with no machine data. Both need this farm's acreage keyed to CLUs. <b>Not measured is not zero</b>; treat this card as harvest progress only.
                        </div>
                      )}
                      {a.unmatched_acres>=5 && (
                        <div style={{ marginTop:8, fontSize:11.5, color:"#8a5a00", background:"#fff8ec", border:"1px solid #f0dcae", borderRadius:7, padding:"6px 10px" }}>
                          {a.unmatched_acres} ac of {fmCropName(a.crop)} harvested in {a.county} that isn't on an insured CLU — review before it counts (custom combining for someone else, or unreported/added land).
                          {/* NAME the fields and open them. A flagged acreage you cannot locate
                              is a to-do that never gets done; one click to the map is the
                              difference between "review this" and actually reviewing it. */}
                          {(a.unmatched_fields || []).length > 0 && (
                            <div style={{ marginTop:5, display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                              {a.unmatched_fields.map(f => (
                                <button key={f.jd_field_id} onClick={() => openFieldOnMap(f.jd_field_id, a.crop, a.year)}
                                        title={`Open ${f.field} on the map — ${f.acres} ac with no insured CLU`}
                                        style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:6, cursor:"pointer",
                                                 border:"1px solid #e0cf9a", background:"#fff", color:"#8a5a00" }}>
                                  {f.field} · {f.acres} ac
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {a.missing_data_acres>=5 && (
                        <div style={{ marginTop:8, fontSize:11.5, color:"#6a5a2a", background:"#fbf7ec", border:"1px solid #ecdfb8", borderRadius:7, padding:"6px 10px" }}>
                          {a.missing_data_acres} ac of insured {fmCropName(a.crop)} fields have no machine harvest data yet{a.missing_fields&&a.missing_fields.length?` — e.g. ${a.missing_fields.slice(0,4).map(f=>`FSN ${f.fsn??"—"} Fld ${f.field??"—"} (${f.acres}ac)`).join(", ")}${a.missing_fields.length>4?"…":""}`:""}. {a.pipeline_pending_ops > 0 ? ` ${a.pipeline_pending_ops} ${fmCropName(a.crop)} harvest operation${a.pipeline_pending_ops===1?" is":"s are"} on file with no map built (${a.pipeline_pending_acres} ac) — that part is OUR processing, not the grower's. Recompute maps before chasing anyone.` : " If they're done combining, we may be missing data (custom harvester without yield mapping) — chase these before trusting a claim read."}
                        </div>
                      )}
                      {(a.harvested_acres - a.insured_acres) >= 5 && a.acre_coverage > 1.05 && (
                        <div style={{ marginTop:8, fontSize:11.5, color:"#8a5a00", background:"#fff8ec", border:"1px solid #f0dcae", borderRadius:7, padding:"6px 10px" }}>
                          Machine harvested <b>{(a.harvested_acres - a.insured_acres).toFixed(1)} ac more</b> {fmCropName(a.crop)} on these fields than is insured — possible added or under-reported land; verify the insured acres before finalizing.
                        </div>
                      )}
                      {!a.complete && <div style={{ marginTop:6, fontSize:11, color:"#677167" }}>Claim check holds until every insured field is combined ({a.fields_harvested}/{a.fields_total} in).</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
    </>
  );
  // ⚠ THE 16px RULE GOES WHERE THE BOXES ARE. It lived only on the full-screen sheet, so
  // inside Field Mapper every filter box (field search, dates, county, scale) was 11-12px
  // and an iPhone zoomed the page the moment a farmer tapped one.
  if (embedded) return <div className="jd-fdv" style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8e0", overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
    {isMobile && <style>{".jd-fdv input, .jd-fdv select { font-size: 16px !important; }"}</style>}{inner}</div>;
  // ⚠ RENDERED INTO document.body, NEVER WHERE IT WAS CALLED. It used to be a
  // position:fixed pop-up inside whatever opened it — on a phone that is the portal's
  // scrolling middle pane, and Kevin's screenshot (2026-10-31, previewing a farmer)
  // shows the app header and bottom bar painted over it, its own close button scrolled
  // out of reach, and nothing scrolling. Live Bids solved the same thing the same way.
  // On a phone it is a full-screen sheet: a Back bar that never scrolls away, and ONE
  // scrolling body. zIndex 10000 clears the preview banner (9999), which otherwise
  // sits on top of the Back button.
  const sheet = isMobile ? (
    <div role="dialog" aria-modal="true" aria-label={`Machine data — ${farmer.name}`}
      className="jd-sheet" style={{ position:"fixed", inset:0, zIndex:10000, background:"#fff", display:"flex", flexDirection:"column", fontFamily:T.sans, color:T.ink }}>
      {/* iPhone zooms the whole page into any box under 16px the moment it's tapped. */}
      <style>{".jd-sheet input, .jd-sheet select { font-size: 16px !important; }"}</style>
      {/* The Field Mapper page header (Kevin, 2026-10-31: the machine data page had
          "completely different text and look"): Back names where it goes, then the
          title in the same serif as a field's page. The portal drops the shell font,
          so the sheet sets its own. */}
      <div style={{ flexShrink:0, padding:"calc(env(safe-area-inset-top,0px) + 6px) 16px 10px", background:"#fff", borderBottom:"1px solid #eef2ee" }}>
        <button onClick={onClose}
          style={{ height:36, border:"none", background:"none", padding:0, fontFamily:"inherit", fontSize:13.5, fontWeight:800, color:T.green, cursor:"pointer", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          ‹ {backLabel || "Back"}</button>
        <div style={{ fontFamily:T.serif, fontSize:22, fontWeight:700, lineHeight:1.15, color:T.ink }}>Machine data</div>
        <div style={{ fontSize:13, color:"#4f6a4c", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{farmer.name}</div>
      </div>
      <div style={{ flex:1, minHeight:0, overflowY:"auto", overscrollBehavior:"contain", WebkitOverflowScrolling:"touch", paddingBottom:"env(safe-area-inset-bottom,0px)" }}>{inner}</div>
    </div>
  ) : (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:5000, background:"rgba(20,32,20,0.55)", display:"flex", justifyContent:"center", alignItems:"flex-start", overflow:"auto", padding:"3vh 2vw", fontFamily:T.sans, color:T.ink }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:12, maxWidth:1020, width:"100%", boxShadow:"0 12px 48px rgba(0,0,0,0.3)", overflow:"hidden" }}>{inner}</div>
    </div>
  );
  return typeof document !== "undefined" && document.body ? createPortal(sheet, document.body) : sheet;
}

// ─────────────────────────────────────────────────────────────────────────────
// PLANTING PACE CHART — the rolled-up, historical view of our planting-survey
// data: cumulative % planted over the season (our surveyed book) vs NASS NY state
// pace vs the 5-yr NASS average, per crop. Same public rollup shown on the
// standalone Planting Survey page; reused here so Field Progress carries the
// historical pace/trend, not just today's snapshot. (The corn mid-week nowcast
// stays on the survey page.)
// ─────────────────────────────────────────────────────────────────────────────
function PlantingPaceChart({ isMobile = false }) {
  const [crop, setCrop] = useState("Corn");
  const [farms, setFarms] = useState([]);
  const [progress, setProgress] = useState([]);
  const [intentions, setIntentions] = useState([]);
  const [nass, setNass] = useState([]);
  useEffect(() => {
    let alive = true;
    Promise.all([
      sbFetch("planting_survey_farmers", { select: "id,is_active", filters: ["is_active=eq.true"] }).catch(() => []),
      sbFetch("planting_progress", { select: "survey_farmer_id,crop,acres_planted,submitted_at", order: "submitted_at.asc,id.asc" }).catch(() => []),
      sbFetch("planting_intentions", { select: "survey_farmer_id,crop,intended_acres" }).catch(() => []),
      sbFetch("nass_data", { select: "crop,year,week,value,stat_type", filters: ["stat_type=eq.PCT_PLANTED", "state=eq.NY", "crop=in.(CORN,SOYBEANS)"] }).catch(() => []),
    ]).then(([f, p, i, n]) => { if (!alive) return; setFarms(f || []); setProgress(p || []); setIntentions(i || []); setNass(n || []); });
    return () => { alive = false; };
  }, []);

  const CROP_COLORS = { Corn: "#D97706", Soybeans: "#059669" };
  const accent = CROP_COLORS[crop] || "#2d6a2d";
  const series = useMemo(() => {
    const eq = (a, b) => (a || "").toLowerCase() === (b || "").toLowerCase();
    const intentMap = {}; intentions.forEach(x => { (intentMap[x.survey_farmer_id] ||= {})[(x.crop || "").toLowerCase()] = parseFloat(x.intended_acres); });
    const activeIds = new Set(farms.map(f => f.id));
    let totalIntended = 0; activeIds.forEach(id => { const v = intentMap[id]?.[crop.toLowerCase()]; if (v) totalIntended += v; });
    // Rolled-up survey pace over time (cumulative planted ÷ total intended).
    const cropProg = progress.filter(p => eq(p.crop, crop) && activeIds.has(p.survey_farmer_id));
    const dates = [...new Set(cropProg.map(p => new Date(p.submitted_at).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })))].sort((a, b) => new Date(`2026/${a}`) - new Date(`2026/${b}`));
    const book = dates.map(ds => {
      const dObj = new Date(`2026/${ds}`); dObj.setHours(23, 59, 59, 999);
      let planted = 0;
      activeIds.forEach(id => {
        const subs = cropProg.filter(p => p.survey_farmer_id === id && new Date(p.submitted_at) <= dObj).sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
        if (subs.length) planted += parseFloat(subs[0].acres_planted) || 0;
      });
      return { t: dObj.getTime(), pct: totalIntended > 0 ? Math.min(100, Math.round(planted / totalIntended * 100)) : 0 };
    });
    // NASS: 5-yr avg (carry-forward) + current-year state pace, weeks 14–27.
    const nassKey = crop === "Corn" ? "CORN" : "SOYBEANS";
    const byWeek = {}; nass.filter(r => r.crop === nassKey && r.week != null).forEach(r => { (byWeek[r.week] ||= {})[r.year] = r.value; });
    const jan4 = new Date(2026, 0, 4);
    const yearLast = {}; const nassRows = [];
    Object.keys(byWeek).map(Number).filter(w => w >= 14 && w <= 27).sort((a, b) => a - b).forEach(wk => {
      const yd = byWeek[wk];
      for (const [yr, val] of Object.entries(yd)) if (yr !== "2026" && val != null) yearLast[yr] = val;
      const priors = Object.entries(yearLast).filter(([yr]) => yr !== "2026").map(([, v]) => v);
      const s = new Date(jan4); s.setDate(jan4.getDate() + (wk - 1) * 7);
      nassRows.push({ t: s.getTime(), avg: priors.length ? Math.round(priors.reduce((a, b) => a + b, 0) / priors.length) : null, cur: yd[2026] != null ? yd[2026] : null });
    });
    return { book, nass: nassRows, totalIntended, latest: book.length ? book[book.length - 1] : null };
  }, [progress, intentions, farms, nass, crop]);

  const W = 680, H = isMobile ? 190 : 230, PAD = { l: 30, r: 12, t: 12, b: 24 };
  const x0 = new Date(2026, 3, 1).getTime(), x1 = new Date(2026, 6, 12).getTime();
  const xOf = t => PAD.l + (W - PAD.l - PAD.r) * Math.max(0, Math.min(1, (t - x0) / (x1 - x0)));
  const yOf = v => PAD.t + (H - PAD.t - PAD.b) * (1 - Math.max(0, Math.min(100, v)) / 100);
  const line = pts => pts.length ? "M" + pts.map(p => `${xOf(p.t).toFixed(1)},${yOf(p.v).toFixed(1)}`).join("L") : "";
  const bookPts = series.book.map(p => ({ t: p.t, v: p.pct }));
  const curPts = series.nass.filter(r => r.cur != null).map(r => ({ t: r.t, v: r.cur }));
  const avgPts = series.nass.filter(r => r.avg != null).map(r => ({ t: r.t, v: r.avg }));
  const months = [[new Date(2026, 3, 1), "Apr"], [new Date(2026, 4, 1), "May"], [new Date(2026, 5, 1), "Jun"], [new Date(2026, 6, 1), "Jul"]];
  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 12, padding: isMobile ? 14 : 18 };
  const hasData = bookPts.length > 0 || curPts.length > 0;

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a" }}>📈 Survey Planting Pace</div>
          <div style={{ fontSize: 11, color: "#60735f" }}>Our surveyed book vs NASS NY state pace &amp; 5-yr average</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["Corn", "Soybeans"].map(c => (
            <button key={c} onClick={() => setCrop(c)} style={{ cursor: "pointer", border: "1px solid #cdd9cd", borderRadius: 7, padding: "5px 12px", fontSize: 12, fontWeight: 700, background: crop === c ? "#2d4a2d" : "#f6faf6", color: crop === c ? "#fff" : "#2d4a2d" }}>{c}</button>
          ))}
        </div>
      </div>
      {series.latest && (
        <div style={{ fontSize: 12.5, color: "#3a4f3a", marginBottom: 6 }}>
          Book <b style={{ color: accent, fontSize: 15 }}>{series.latest.pct}%</b> planted
          {series.totalIntended > 0 ? <span style={{ color: "#677167" }}> · {Math.round(series.totalIntended).toLocaleString()} ac intended</span> : null}
        </div>
      )}
      {!hasData ? (
        <div style={{ fontSize: 12.5, color: "#677167", padding: "18px 0" }}>No survey pace data for {crop} yet.</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
          {[0, 25, 50, 75, 100].map(g => (
            <g key={g}>
              <line x1={PAD.l} y1={yOf(g)} x2={W - PAD.r} y2={yOf(g)} stroke="#eef2ee" strokeWidth="1" />
              <text x={PAD.l - 4} y={yOf(g) + 3} textAnchor="end" fontSize="9" fill="#677167">{g}</text>
            </g>
          ))}
          {months.map(([d, lab]) => (
            <text key={lab} x={xOf(d.getTime())} y={H - 8} textAnchor="middle" fontSize="9.5" fill="#677167">{lab}</text>
          ))}
          {avgPts.length > 0 && <path d={line(avgPts)} fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5,4" />}
          {curPts.length > 0 && <path d={line(curPts)} fill="none" stroke="#2563eb" strokeWidth="2.5" />}
          {bookPts.length > 0 && <path d={line(bookPts)} fill="none" stroke={accent} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />}
          {bookPts.map((p, i) => <circle key={i} cx={xOf(p.t)} cy={yOf(p.v)} r="3" fill={accent} />)}
        </svg>
      )}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6, fontSize: 11, color: "#5a6e5a" }}>
        <span><span style={{ display: "inline-block", width: 18, height: 3, background: accent, borderRadius: 2, verticalAlign: "middle", marginRight: 5 }} />Our book</span>
        <span><span style={{ display: "inline-block", width: 18, height: 3, background: "#2563eb", verticalAlign: "middle", marginRight: 5 }} />NASS NY 2026</span>
        <span><span style={{ display: "inline-block", width: 18, height: 3, background: "#94a3b8", verticalAlign: "middle", marginRight: 5 }} />5-yr avg</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HARVEST TRACKER — book-wide, agent-facing. Surfaces every JD-connected
// operation's harvest pace + average yield + likely claims in ONE screen, so the
// agent can see the whole book combining in real time and pounce on claims the
// moment a field reads under guarantee. Reads jd-claimwatch with no ?farmer=
// (returns all active connections). The heavy lifting (CLU-matched production,
// completeness, guarantee compare, unmatched/added-land flags) is already done
// server-side in hClaimWatch — this view aggregates + ranks. Kevin: "we should be
// tracking harvest pace and average yield by operation."
// ─────────────────────────────────────────────────────────────────────────────
// Corn maturity: predicted black-layer date per field from plant date + variety RM +
// this year's GDU (actual → 30-yr climatology forward), read from the corn_maturity_2026
// DB view. Shows a book-wide roll-up across all connected farms plus a per-farm drill.
const CM_STATUS = {
  // A field that is OFF is not a forecast state at all — it outranks every other
  // status, because nothing about maturity is still a question once it is in the bin.
  harvested:   { label: "Harvested",    c: "#1a5e8a", bg: "#e4eef6" },
  mature:      { label: "Mature",       c: "#1f7a3d", bg: "#e6f2e8" },
  on_track:    { label: "On track",     c: "#2d8a4a", bg: "#eaf4ea" },
  frost_risk:  { label: "Frost risk",   c: "#a9791f", bg: "#f6efdd" },
  wont_finish: { label: "Won't finish", c: "#c0392b", bg: "#f7e7e4" },
  // ⚠ NOT A VERDICT — THE ABSENCE OF ONE. Grey on purpose: an unknown maturity
  // is neither good news nor bad, and colouring it would state something.
  unknown:     { label: "Maturity unknown", c: "#6b6f63", bg: "#f0f1ee" },
};
// Silage chop-window status (whole-plant DM), used when a field is on the silage clock.
const CM_SIL = {
  sil_approaching: { label: "Approaching", c: "#2d8a4a", bg: "#eaf4ea" },
  sil_ready:       { label: "Chop window", c: "#1f7a3d", bg: "#e6f2e8" },
  sil_past:        { label: "Getting dry",  c: "#a9791f", bg: "#f6efdd" },
  unknown:         { label: "Maturity unknown", c: "#6b6f63", bg: "#f0f1ee" },
};
// ⚠ AN UNMAPPED HYBRID IS NOT A 100-DAY HYBRID, AND EVERY DATE ON THE ROW SAYS
// IT IS. `corn_maturity_2026` falls back to 2,500 GDU when no RM resolves — a
// 100-day hybrid — so the field still gets a black-layer date, a frost verdict
// and a harvest window, and nothing on screen says the maturity was assumed.
// Measured on the live book 2026-09-07, after a source-first recovery pass closed
// 23 hybrids: 69 seeding passes over 2,199 acres across 8 farms.
//
// ONE RULE, EVERY SURFACE. The table, the tiles, the curve, the printed sheet
// and the Excel export all ask this — a blank on screen beside a confident date
// on the sheet a grower is handed is the same defect somewhere nobody re-checks.
//
// IT READS BOTH SIGNALS ON PURPOSE. `variety_unmapped` is what the view has
// always emitted; `status === 'unknown'` is what it emits once sql/059 lands.
// Either alone answers correctly, so this is right before and after that
// migration and cannot depend on their deploy order.
const cmRmUnknown = (r) => !!r && (r.status === "unknown" || !!r.variety_unmapped);
// Year-aware short date: "Oct 21", or "Feb 27 ’27" when the projection rolls into
// next year — which is a real answer here (a late-planted field that will not dry in
// season), so the year must never be dropped. Hoisted to module scope because the
// hand-entry rows print the same dates as the table above them and the two must not
// drift into two formats.
const cmShortDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  const s = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return dt.getFullYear() === 2026 ? s : `${s} ’${String(dt.getFullYear()).slice(2)}`;
};
// ── ONE FIELD, SEVERAL PLANTING DATES (Kevin, 2026-10-26) ─────────────────────
// "If there are multiple planting dates for a field, show each planting date with its
// own maturation data because planting date definitely matters, just label them as
// such." The view is one row per planting (sql/109 removed John Deere's reissued
// copies), so a field planted on two days is two rows with two maturities — 73 fields
// on the live 2026 book — and nothing on either row said the other existed.
//
// Keyed on the FIELD, the same key the split-planter badge uses: owner + JD field id,
// or the CLU for a hand entry. Numbered by DISTINCT plant date, earliest first, so two
// hybrids drilled the same day are one planting and a split planter is not mistaken
// for a second planting. Returns opId -> { k, n, date } for fields with n > 1 only.
function cmPlantingsByField(rows) {
  const byField = new Map();
  for (const r of rows || []) {
    if (!r || !r.plant_date) continue;
    const f = r.jd_field_id || (r.clu_id ? String(r.clu_id).toLowerCase() : null);
    if (!f) continue;
    const key = (r.owner_farmer_id || r.farmer_id || "") + "|" + f;
    const g = byField.get(key) || { dates: new Set(), rows: [] };
    g.dates.add(String(r.plant_date).slice(0, 10)); g.rows.push(r);
    byField.set(key, g);
  }
  const out = new Map();
  for (const [key, g] of byField) {
    if (g.dates.size < 2) continue;
    const dates = [...g.dates].sort();
    for (const r of g.rows) {
      const d = String(r.plant_date).slice(0, 10);
      out.set(r.jd_operation_id, { k: dates.indexOf(d) + 1, n: dates.length, date: d, field: key });
    }
  }
  return out;
}
const cmPlantingLabel = (p) => p ? `Planting ${p.k} of ${p.n} · ${cmShortDate(p.date)}` : "";
// Keep a multi-planting field's rows TOGETHER, in plant-date order, wherever the
// current sort first places the field — so "Planting 1 of 2" sits beside "2 of 2"
// instead of wherever its own harvest date sorted it. Every other row keeps its place.
function cmGroupPlantings(list, plantings) {
  const out = [], done = new Set();
  for (const r of list || []) {
    const p = plantings && plantings.get(r.jd_operation_id);
    if (!p) { out.push(r); continue; }
    if (done.has(p.field)) continue;
    done.add(p.field);
    const mates = list.filter((x) => { const q = plantings.get(x.jd_operation_id); return q && q.field === p.field; });
    mates.sort((a, b) => String(a.plant_date).localeCompare(String(b.plant_date)) || String(a.variety || "").localeCompare(String(b.variety || "")));
    out.push(...mates);
  }
  return out;
}
// ── ENTER A PLANTING BY HAND ─────────────────────────────────────────────────
// For a grower with NO machine data: their insured corn fields, each with a picture
// of its own boundary, and a plant date + hybrid to fill in. 166 insureds are in
// that position with 118,879 CY2026 corn acres between them.
//
// The picture is an inline SVG of the CLU outline, drawn from a path the server
// normalises — not a map tile. In a list of two hundred rows a tile per row would be
// two hundred network requests and a key to manage; a boundary SHAPE is what tells a
// grower which field they are looking at, and it renders instantly and offline.
//
// A field with no weather cell within 20 miles is shown but NOT editable, with the
// reason: the model reads heat units from the field's grid cell, so there is nothing
// to project from. Letting them type a date that silently produced a Pennsylvania
// field's harvest date off Batavia weather is the failure this avoids.
// A DATE TYPED IN HERE HAS TO SHOW ITS ANSWER HERE. The projection has always run
// on a hand-entered planting — corn_maturity_2026's `corn_manual` branch takes the
// same GDU model through the field's own weather cell, and Pagen's four entries
// return full chop windows — but it renders in the TABLE ABOVE this form, and on a
// farm with no machine data that table is empty until the first row lands. So the
// grower typed a date, got a Save button that greyed out, and nothing else: the one
// person the hand-entry form exists for could not tell whether it had worked.
// `proj` is that field's row from the SAME maturity fetch the table draws, keyed by
// CLU — no second query, and it refreshes on the reload the save already triggers.
function CluFieldRow({ f, farmerId, year, onSaved, disabled, proj = null, moist = "23" }) {
  // WE ALREADY KNOW WHEN THIS FIELD WENT IN — don't make them type it again.
  // `aip_plant_date` is P27 #24 off the acreage report, present on 92.7% of CY2026
  // corn fields. The row OPENS on it, so the only thing left to fill is the hybrid
  // and its RM, which is the one input the AIP file does not carry and the one that
  // actually sets the black-layer target. Kevin, 2026-08-20: "don't we already have
  // their plant dates for each field?" — we do, on 23 of Pagen's 23, and they had
  // re-keyed all 23 by hand, matching ours exactly on 20.
  const prefill = f.plant_date || f.aip_plant_date || "";
  const [pd, setPd] = useState(prefill);
  const [variety, setVariety] = useState(f.variety || "");
  const [rm, setRm] = useState(f.rm_days != null ? String(f.rm_days) : "");
  const [use, setUse] = useState(f.harvest_use || "grain");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  // DIRTY IS MEASURED AGAINST WHAT THE ROW OPENED SHOWING, not against the stored
  // hand entry. Comparing to `f.plant_date` would light up Save on every prefilled
  // row the moment the list rendered — two hundred rows all claiming unsaved
  // changes nobody made.
  const dirty = (pd || "") !== prefill || (variety || "") !== (f.variety || "")
    || (rm || "") !== (f.rm_days != null ? String(f.rm_days) : "") || (use || "grain") !== (f.harvest_use || "grain");
  // The grower has moved the date off the acreage report's. Not an error — a replant
  // or a correction the report has not caught up with — but the agent needs to see
  // it, so the row says what the report holds rather than silently disagreeing.
  const dateMoved = !!(f.aip_plant_date && pd && pd !== f.aip_plant_date);
  const save = async (clear) => {
    setSaving(true); setErr(null);
    try {
      const qs = `farmer=${encodeURIComponent(farmerId)}&clu=${encodeURIComponent(f.clu_id)}&year=${year}`
        + (clear ? "&clear=1"
                 : `&plant_date=${encodeURIComponent(pd)}&variety=${encodeURIComponent(variety.trim())}&rm=${encodeURIComponent(rm.trim())}&use=${use}`);
      const r = await authedFetch(`/api/market?fn=jd-manual-planting&${qs}`, { method: "POST" });
      const j = await asJson(r);
      if (!r.ok || (j && j.error)) throw new Error((j && j.error) || `HTTP ${r.status}`);
      // Clearing removes the HAND entry, so the row falls back to what the acreage
      // report says — not to blank. Blanking would read as "we know nothing about
      // this field", which is the opposite of true.
      if (clear) { setPd(f.aip_plant_date || ""); setVariety(""); setRm(""); setUse("grain"); }
      onSaved && onSaved();
    } catch (e) { setErr(String((e && e.message) || e)); }
    finally { setSaving(false); }
  };
  const cell = { padding: "8px 10px", fontSize: 12.5, borderBottom: "1px solid #eef2ee", verticalAlign: "middle" };
  const inp = { fontSize: 12, padding: "5px 7px", border: "1px solid #cdd9cd", borderRadius: 6, background: disabled ? "#f4f6f4" : "#fff" };
  const name = [f.fsn && `FSN ${f.fsn}`, f.tract && `T${f.tract}`, f.field_id && `F${f.field_id}`].filter(Boolean).join(" · ") || f.clu_id.slice(0, 8);
  return (
    <tr>
      <td style={{ ...cell, width: 56 }}>
        {f.svg_path ? (
          <svg viewBox="0 0 100 100" width="46" height="46" style={{ display: "block", background: "#f4f8f4", border: "1px solid #e0e9e0", borderRadius: 6 }}
               aria-label={`Boundary of ${name}`}>
            <path d={f.svg_path} fill="#cfe3cf" stroke="#4a7a55" strokeWidth="2.5" strokeLinejoin="round" />
          </svg>
        ) : (
          <div title="No boundary on file for this CLU" style={{ width: 46, height: 46, borderRadius: 6, background: "#f6f6f4", border: "1px dashed #d8ddd8", color: "#a8b2a8", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>no map</div>
        )}
      </td>
      <td style={{ ...cell }}>
        <div style={{ fontWeight: 700, color: "#1a2e1a" }}>{name}</div>
        <div style={{ fontSize: 11, color: "#60735f" }}>
          {f.acres != null ? `${Number(f.acres).toFixed(1)} ac` : "— ac"}{f.county ? ` · ${f.county}` : ""}
          {/* NAME THE CAUSE, because the two need opposite fixes. Unplaced = we hold
              no boundary and no coordinates, so there is nothing to look weather up
              against; that is a shapefile, not a grid. Placed-but-far = the field is
              genuinely outside the grid, which for this book means out-of-state.
              Blaming the grid for a missing boundary sent Kevin looking at Livingston
              County weather for a field the grid covers perfectly well. */}
          {!f.weather_ok && (f.placed === false
            ? <span style={{ color: "#a9791f" }}> · no field boundary on file — nothing to place it on the weather map</span>
            : f.weather_mi == null
              /* Placed, but the distance did not resolve — same fourth state as the
                 Corn Maturity panel. "no weather within 20 mi" would assert a
                 distance nobody measured. */
              ? <span style={{ color: "#8a7340" }}> · location not established against the weather grid</span>
              : <span style={{ color: "#a9791f" }}> · no weather within 20 mi (nearest {f.weather_mi} mi)</span>)}
        </div>
        {/* WHAT THAT DATE PROJECTS TO — the same numbers, from the same model run, as
            a John Deere field gets. Which event is named follows the row's OWN Grain/
            Silage use, because those are different clocks: silage is whole-plant
            dry-down to ~35% DM, roughly two weeks ahead of grain black layer.
            An entry with no RM mapping cannot be projected and says so rather than
            showing a blank that reads like "not ready yet". */}
        {proj && (
          <div style={{ fontSize: 11, marginTop: 3, lineHeight: 1.45 }}>
            {proj.variety_unmapped ? (
              <span style={{ color: "#a9791f" }}>
                No maturity mapping for that hybrid — add its RM and the window appears.
              </span>
            ) : String(proj.harvest_use || "").toLowerCase() === "silage" ? (
              <span style={{ color: (CM_SIL[proj.silage_status] || CM_SIL.sil_approaching).c, fontWeight: 700 }}>
                🌿 Chop window {cmShortDate(proj.silage_early_date)} – {cmShortDate(proj.silage_late_date)}
                <span style={{ fontWeight: 400, color: "#60735f" }}>
                  {" · ideal "}{cmShortDate(proj.silage_date)}
                  {proj.pct_to_maturity != null ? ` · ${proj.pct_to_maturity}% to black layer` : ""}
                </span>
              </span>
            ) : (
              <span style={{ color: (CM_STATUS[proj.status] || CM_STATUS.on_track).c, fontWeight: 700 }}>
                🌽 Harvest ≤{moist}% {cmShortDate(proj["harvest_" + moist + "_date"])}
                <span style={{ fontWeight: 400, color: "#60735f" }}>
                  {" · black layer "}{proj.status === "mature" ? (proj.black_layer_date ? "reached " + cmShortDate(proj.black_layer_date) : "reached") : cmShortDate(proj.black_layer_date)}
                  {proj.pct_to_maturity != null ? ` · ${proj.pct_to_maturity}%` : ""}
                </span>
              </span>
            )}
          </div>
        )}
      </td>
      <td style={{ ...cell }}>
        <input type="date" value={pd} disabled={disabled} onChange={e => setPd(e.target.value)} min="2026-03-15" max="2026-12-31" style={{ ...inp, width: 140 }} />
        {/* Say where the date came from. A prefilled field that looks typed invites a
            grower to trust a number nobody stated, and an agent to read a corrected
            date as the report's own. */}
        {!f.plant_date && f.aip_plant_date && (
          <div style={{ fontSize: 10, color: "#60735f", marginTop: 2 }}>from your acreage report</div>
        )}
        {dateMoved && (
          <div style={{ fontSize: 10, color: "#a9791f", marginTop: 2 }}>
            acreage report says {cmShortDate(f.aip_plant_date)}
          </div>
        )}
        {!f.plant_date && !f.aip_plant_date && (
          <div style={{ fontSize: 10, color: "#60735f", marginTop: 2 }}>no date on the acreage report</div>
        )}
      </td>
      <td style={{ ...cell }}>
        <input value={variety} disabled={disabled} onChange={e => setVariety(e.target.value)} placeholder="Hybrid" style={{ ...inp, width: 130 }} />
      </td>
      <td style={{ ...cell }}>
        <input value={rm} disabled={disabled} onChange={e => setRm(e.target.value.replace(/[^0-9]/g, ""))} placeholder="RM" inputMode="numeric"
               title="Relative maturity in days (e.g. 98) — this is what sets the black-layer target" style={{ ...inp, width: 56 }} />
      </td>
      <td style={{ ...cell, textAlign: "center" }}>
        <span style={{ display: "inline-flex", border: "1px solid #cdd9cd", borderRadius: 7, overflow: "hidden", opacity: disabled ? 0.5 : 1 }}>
          {[["grain", "Grain", "#2d4a2d"], ["silage", "Silage", "#2d8a4a"]].map(([u, lab, on], j) => (
            <button key={u} disabled={disabled} onClick={() => setUse(u)}
              style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", border: "none", borderLeft: j ? "1px solid #cdd9cd" : "none", cursor: disabled ? "default" : "pointer", background: use === u ? on : "#fff", color: use === u ? "#fff" : "#60735f" }}>{lab}</button>
          ))}
        </span>
      </td>
      <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
        {err && <div style={{ color: "#c0392b", fontSize: 11, marginBottom: 3, maxWidth: 190, whiteSpace: "normal" }}>{err}</div>}
        <button onClick={() => save(false)} disabled={disabled || saving || !dirty || !pd}
          title={!pd ? "A plant date is what the projection counts heat units from" : undefined}
          style={{ fontSize: 11.5, fontWeight: 700, padding: "5px 11px", borderRadius: 7, border: "none", cursor: (disabled || saving || !dirty || !pd) ? "default" : "pointer", background: (disabled || !dirty || !pd) ? "#e3ece3" : "#2d4a2d", color: (disabled || !dirty || !pd) ? "#677167" : "#fff" }}>
          {saving ? "…" : f.plant_date ? "Update" : "Save"}
        </button>
        {f.plant_date && !disabled && (
          <button onClick={() => save(true)} disabled={saving} title="Remove this entry"
            style={{ marginLeft: 6, fontSize: 11, padding: "5px 7px", background: "transparent", color: "#a08a6a", border: "none", cursor: "pointer" }}>clear</button>
        )}
      </td>
    </tr>
  );
}

function ManualPlantingSection({ farmerId, farmerName, year = 2026, isMobile, onChanged, projByClu = null, moist = "23", gridLimited = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const [onlyBlank, setOnlyBlank] = useState(false);
  useEffect(() => {
    if (!open || !farmerId) return;
    let alive = true; setData(null); setErr(null);
    authedFetch(`/api/market?fn=jd-clu-fields&farmer=${encodeURIComponent(farmerId)}&year=${year}`)
      .then(r => r.json())
      .then(d => { if (alive) { if (d && d.error) setErr(d.error); else setData(d); } })
      .catch(e => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [open, farmerId, year, reload]);
  const all = (data && data.fields) || [];
  const shown = onlyBlank ? all.filter(f => !f.plant_date) : all;
  const done = all.filter(f => f.plant_date).length;
  const noWx = all.filter(f => !f.weather_ok).length;
  // Split the SAME count by cause. "Outside the weather grid" is true for an
  // out-of-state field and false for one we simply hold no boundary for, and the
  // second is the common case on a NY farm.
  // ⚠ RE-MEASURED 2026-09-11. This read "every one of those in MI/OH/PA", which the
  // 2026-09-02 Pennsylvania build made false — all 1,207 PA corn fields are on the
  // grid now. Off-grid today is MICHIGAN (518 corn fields / 16,781 ac, and ZERO of
  // its 775 insured CLUs within 20 mi — the nearest cell is 242 mi away), plus OH 101
  // and IN 81. The unplaced-boundary half stays the common NY case, 145 fields.
  const noBoundary = all.filter(f => !f.weather_ok && f.placed === false).length;
  const offGrid = noWx - noBoundary;
  // Marked for chopping on the map, but never given a plant date. PAGEN FARMS had
  // 23 of these and no John Deere connection, so the Corn Maturity page read "No
  // 2026 corn planting projected yet" while the grower had already picked those
  // very fields — an absence with no explanation anywhere on the screen.
  const chopNoDate = all.filter(f => f.harvest_use && !f.plant_date).length;
  // …and of those, how many the acreage report can already date. That decides what
  // the grower is actually being asked for: a click to confirm plus a hybrid, or a
  // date typed from memory. They are different asks and must not read the same.
  const chopNoDatePrefilled = all.filter(f => f.harvest_use && !f.plant_date && f.aip_plant_date).length;
  // How much of the whole list we can date without asking — what makes this form a
  // confirm-and-name-the-hybrid job rather than a typing job.
  const haveAip = all.filter(f => !f.plant_date && f.aip_plant_date).length;
  const box = { background: "#fff", border: "1px solid #e4ece4", borderRadius: 12, padding: isMobile ? 12 : 16, marginBottom: 16 };
  if (!farmerId) return null;
  return (
    <div style={box}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>✍️ Confirm plant dates &amp; add hybrids</div>
          <div style={{ fontSize: 12, color: "#60735f", marginTop: 2, maxWidth: 640, lineHeight: 1.45 }}>
            {/* ⚠ BUG-056, THE SECOND HALF. With the headline fixed, this intro was the one
                place on the screen still promising a remedy the grid cannot give: rendered as
                Chris Dawson on RED DAWN FARMING LLC, the headline said a hand-entered plant date
                cannot produce a projection for 87 of 90 fields, and this line directly under it
                said it "joins the projection above". The list itself loads only when opened, so
                it cannot know reach up front; the panel above has already measured it and hands
                it down as `gridLimited`. A farm with nothing off the grid keeps the sentence as
                it was. */}
            No machine data needed. <b>The plant dates are already filled in from your acreage report</b> —
            check one, add the hybrid, and it joins the projection above using that field's own weather
            {gridLimited
              ? <> — <b>but only for a field within reach of the weather grid</b>; the fields outside it are listed with their distance and cannot be dated here.</>
              : "."}
            {" "}{done > 0 && <b>{done} field{done === 1 ? "" : "s"} entered so far.</b>}
          </div>
        </div>
        <button onClick={() => setOpen(o => !o)}
          style={{ fontSize: 12.5, fontWeight: 700, padding: "8px 15px", borderRadius: 8, border: "1px solid #cdd9cd", background: open ? "#2d4a2d" : "#f6faf6", color: open ? "#fff" : "#2d4a2d", cursor: "pointer" }}>
          {open ? "Hide fields" : `Show ${farmerName || "this farm"}'s insured corn fields`}
        </button>
      </div>
      {open && err && <div style={{ marginTop: 12, color: "#c0392b", fontSize: 12.5 }}>Couldn't load fields: {err}</div>}
      {open && !data && !err && <div style={{ marginTop: 12, color: "#60735f", fontSize: 12.5 }}>Reading the acreage report…</div>}
      {open && data && !all.length && (
        <div style={{ marginTop: 12, color: "#60735f", fontSize: 12.5 }}>
          No insured corn fields keyed to a boundary for {year} on this farm{data.note ? ` — ${data.note}` : ""}.
        </div>
      )}
      {open && all.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", margin: "14px 0 8px", fontSize: 12 }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", color: "#3a4f3a", cursor: "pointer", userSelect: "none" }}>
              <input type="checkbox" checked={onlyBlank} onChange={e => setOnlyBlank(e.target.checked)} style={{ accentColor: "#2d6a2d", width: 15, height: 15, cursor: "pointer" }} />
              Only fields I haven't entered
            </label>
            <span style={{ color: "#677167" }}>{shown.length} of {all.length} field{all.length === 1 ? "" : "s"}</span>
            {/* How many rows opened with a date we already held, so it is visible that
                the form is asking for the hybrid and not for the calendar. */}
            {haveAip > 0 && <span style={{ color: "#60735f" }}>{haveAip} dated from your acreage report</span>}
            {/* Never silently drop a field the model can't run — say how many and why. */}
            {noBoundary > 0 && <span style={{ color: "#a9791f" }}>{noBoundary} with no field boundary — can't be placed on the weather map</span>}
            {offGrid > 0 && <span style={{ color: "#a9791f" }}>{offGrid} outside the weather grid — can't be projected</span>}
          </div>
          {/* Say why a field the grower already picked is missing from the projection.
              Marking one for chopping records the INTENT and nothing else; the model
              needs a plant date before it can run. Without this line the page just
              reads empty and the grower has no way to know one more field would fix it. */}
          {chopNoDate > 0 && (
            <div style={{ margin: "0 0 10px", padding: "9px 11px", borderRadius: 7,
                          background: "#fdf6e8", borderLeft: "4px solid #a9791f",
                          fontSize: 12.5, lineHeight: 1.5, color: "#6a5a3a" }}>
              <b style={{ color: "#7a5a10" }}>{chopNoDate} field{chopNoDate === 1 ? " is" : "s are"} marked for chopping but {chopNoDate === 1 ? "isn't" : "aren't"} in the projection yet.</b>{" "}
              Marking a field for silage records what you intend to do with it — the maturity
              projection also needs to know when it went in and what hybrid.
              {chopNoDatePrefilled > 0 && <>
                {" "}<b>{chopNoDatePrefilled === chopNoDate ? "The plant dates are" : `${chopNoDatePrefilled} of those plant dates are`} already filled in from your acreage report</b> —
                add the hybrid and hit Save.
              </>}
              {chopNoDatePrefilled < chopNoDate && <>
                {" "}The other {chopNoDate - chopNoDatePrefilled} {chopNoDate - chopNoDatePrefilled === 1 ? "has" : "have"} no
                date on the acreage report, so {chopNoDate - chopNoDatePrefilled === 1 ? "it needs" : "they need"} one typed in.
              </>}
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid #eef2ee", borderRadius: 10 }}>
            <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse", background: "#fff" }}>
              <thead><tr style={{ background: "#f6faf6" }}>
                {["", "Field", "Planted", "Hybrid", "RM", "Use", ""].map((h, i) => (
                  <th key={i} style={{ padding: "8px 10px", fontSize: 10.5, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", color: "#60735f", borderBottom: "2px solid #cdd9cd", textAlign: h === "Use" ? "center" : "left" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {shown.map(f => (
                  <CluFieldRow key={f.clu_id} f={f} farmerId={farmerId} year={year} disabled={!f.weather_ok}
                    proj={projByClu ? (projByClu[String(f.clu_id || "").toLowerCase()] || null) : null} moist={moist}
                    onSaved={() => { setReload(n => n + 1); onChanged && onChanged(); }} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// `portal` = a farmer is reading this. The maturity ROWS are caller-scoped
// server-side (jd-maturity → gateScoped).
//
// THE GRAIN/SILAGE TOGGLE IS NOW THE GROWER'S TO SET (Kevin, 2026-08-16) — they are
// the ones who know what they intend to chop. It writes the SAME value the agent
// sets, so the page carries one number and the last writer wins; `updated_by_role`
// on the row is what says who chose it. `jd-field-use` resolves the operation to the
// OWNER of the CLU and refuses anything outside the caller's own farms.
//
// It used to be read-only here AND `requireAdmin` on the server, which is the
// combination that made it look broken rather than absent: the button rendered live,
// clicked, and silently did nothing. A control a caller may not use is now disabled
// and says so — a dead-but-live-looking affordance is worse than no affordance.
//
// The VARIETY editor stays agent-only. An RM correction is applied book-wide to
// every field of that hybrid, so it is not one grower's call to make.
// ⚠ BUG-056, REOPENED BY RUNNING IT AS THE USER (2026-09-11). #131 gave the Corn
// Maturity empty state two answers — "nothing here can be projected" and the original
// invitation — and chose the first only when EVERY dropped field was provably off the
// weather grid. Signed in as Chris Dawson on RED DAWN FARMING LLC, the page still
// printed the invitation ("Machine data fills this in on its own — or enter a plant
// date and hybrid by hand below"): 87 of its 90 dropped fields sit 286 miles from the
// nearest cell and 3 have no John Deere boundary yet, so the all-or-nothing test never
// fired and both impossible remedies went back to the grower. #131's own fixtures had
// all 90 off the grid; the real farm did not.
//
// A THIRD CASE, not a looser threshold. "some" says how many CANNOT be projected and
// states only what is KNOWN about the rest — a field with no boundary has no known
// location, and nothing here claims it will or will not land on the grid. The
// invitation survives only where no dropped field is provably off the grid, which
// keeps #131's rule that a selection merely waiting on boundaries is not told to give
// up. `all` is unchanged: off the grid, or not measurable against it, for every one.
function maturityEmptyCase(d) {
  if (!d || !(d.total > 0)) return "none";
  if (d.offGrid + d.unknownWx >= d.total) return "all";
  if (d.offGrid > 0) return "some";
  return "none";
}

// Did the jd-maturity read FAIL? Returns the reason, or null for a usable payload.
// A failed read must never fall through to the empty-planting state: on 2026-10-19
// an HTTP 500 (the view passing PostgREST's 8 s timeout) rendered as "No 2026 corn
// planting projected yet" on a book with 1,354 projected fields. Three ways to fail,
// and a payload must clear all three — a 200 with no `rows` array is not "no corn".
function cornMaturityLoadError(r, d) {
  if (!r || !r.ok) return (d && d.error) ? String(d.error) : `the server answered HTTP ${r ? r.status : "?"}`;
  if (d && d.error) return String(d.error);
  if (!d || !Array.isArray(d.rows)) return "the server sent no field list";
  return null;
}

function CornMaturityPanel({ farmers = [], isMobile = false, isPortal = false, restrictFarmerIds = null }) {
  const [rows, setRows] = useState(null);
  const [dropped, setDropped] = useState(null); // corn seedings the VIEW dropped — see below
  const [notTheirs, setNotTheirs] = useState(null); // fields set aside as NOT this grower's ground
  const [err, setErr] = useState(null);
  const [farmSel, setFarmSel] = useState("__all__");
  const [reloadKey, setReloadKey] = useState(0);
  const [edit, setEdit] = useState(null); // { op, variety, rm } while editing one field
  const [saving, setSaving] = useState(false);
  const [moist, setMoist] = useState("23"); // harvest-moisture target (23% = default harvest start)
  const [cutoff, setCutoff] = useState(305); // "ready by" date, as 2026 day-of-year (Nov 1)
  const [reqRm, setReqRm] = useState(true); // require a known RM + plant date
  const [hideSmall, setHideSmall] = useState(true); // drop fields <= 3 ac
  const [mode, setMode] = useState("plan"); // grain | silage | plan; default plan so per-field toggles show immediately
  const [savingUse, setSavingUse] = useState(null); // op id being toggled grain/silage
  const [sortBy, setSortBy] = useState({ key: "event", dir: "asc" }); // "event" = soonest harvest/chop, the long-standing default
  const [savedNote, setSavedNote] = useState(null); // what the last variety/RM save actually did
  const [statusSel, setStatusSel] = useState(null); // a status tile clicked = show only those fields
  useEffect(() => {
    let alive = true;
    // Read through the service-role JD backend, not a direct view read: the
    // jd_* base tables are RLS-on/no-policy, so a browser sbFetch on the
    // security_invoker view comes back empty. jd-maturity scopes to the
    // caller (admin → all farms, portal user → own).
    // Name the farms when we have them: the handler scopes a real farmer, but a
    // portal screen under "Preview as" calls it with an ADMIN token, which is
    // legitimately the whole book unless the request narrows itself.
    const ids = (restrictFarmerIds || []).filter(Boolean);
    const q = ids.length ? `&farmer=${ids.map(encodeURIComponent).join(",")}` : "";
    if (isPortal && !q) { setRows([]); return; }
    // ⚠ A FAILED READ IS NOT AN EMPTY FARM (2026-10-19). This used to be
    // `.then(r => r.json())` with no status check, so an HTTP 500 body
    // ({"error": …}) became `rows = []` and rendered "No 2026 corn planting
    // projected yet" — telling Kevin a book with 1,354 projected fields had none.
    // cornMaturityLoadError() is the one rule: a non-2xx, an `error` field, or a
    // payload with no `rows` array is a failure, and the failure is SHOWN.
    setErr(null);
    authedFetch(`/api/market?fn=jd-maturity${q}`)
      .then(async r => ({ r, d: await asJson(r) }))
      .then(({ r, d }) => {
        if (!alive) return;
        const why = cornMaturityLoadError(r, d);
        if (why) { setErr(why); return; }
        setRows(d.rows); setDropped(d.dropped || null); setNotTheirs(d.excluded || null);
      })
      .catch(e => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [reloadKey, restrictFarmerIds, isPortal]);
  // A GROWER MAY EDIT THIS. They are the ones who know what went through the
  // planter — JD reports `---`, the crop name or a bare brand on half the seeding
  // rows, and a field with no RM has nothing to project, so it drops off the chart
  // entirely. `jd-variety-set` scopes the write to the operation's own farm, so
  // this is safe to render for a portal user; it is no longer !isPortal-gated.
  // The one asymmetry lives on the SERVER: an agent's entry is learned into the
  // book-wide corn_hybrids map, a grower's is applied only across their own
  // fields. `applied` is what came back, and it gets said out loud.
  const saveVariety = async () => {
    if (!edit || !edit.op) return;
    setSaving(true);
    try {
      const qs = `op=${encodeURIComponent(edit.op)}&variety=${encodeURIComponent((edit.variety || "").trim())}&rm=${encodeURIComponent((edit.rm || "").toString().trim())}`;
      const r = await authedFetch(`/api/market?fn=jd-variety-set&${qs}`, { method: "POST" });
      const j = await asJson(r);
      if (!r.ok || (j && j.error)) throw new Error((j && j.error) || `HTTP ${r.status}`);
      const n = Number(j && j.applied) || 1;
      setSavedNote((j && j.cleared ? "Override cleared — back to what John Deere sent."
        : n > 1 ? `Saved. RM applied to ${n} fields of that hybrid.`
        : j && j.hybrid_learned ? "Saved — that hybrid's RM is now mapped book-wide."
        : "Saved.")
        // The projection is STORED (sql/101). A save whose refresh did not land is
        // kept, but the dates below are the previous run's until the next refresh.
        + (j && j.maturity_stale ? " The dates below update at the next refresh." : ""));
      setEdit(null); setReloadKey(k => k + 1);
    } catch (e) { alert("Couldn't save variety: " + String((e && e.message) || e)); }
    finally { setSaving(false); }
  };
  // "Send my plan to the agent." TWO STEPS, NEVER ONE (Kevin, 2026-08-19): it files a
  // permanent record and mails the office, so the grower sees exactly what is about to
  // leave and confirms it. The preview is the server's OWN ?dry=1 — it resolves and
  // totals everything through the identical code path and stops before the write and
  // the send, so what is previewed is what gets mailed, not a second count computed
  // here that could disagree with it.
  //
  // The totals are never posted from this client either way: a client that posts its
  // own acres is a client that can post someone else's.
  const [planBusy, setPlanBusy] = useState(false);
  const [planSent, setPlanSent] = useState(null);
  const [planPreview, setPlanPreview] = useState(null);   // the ?dry=1 result awaiting confirmation
  const previewPlan = async () => {
    if (!manualFarmerId || planBusy) return;
    setPlanBusy(true); setPlanSent(null);
    try {
      const r = await authedFetch(
        `/api/market?fn=jd-field-use-plan&farmer=${encodeURIComponent(manualFarmerId)}&year=2026&dry=1`,
        { method: "POST" });
      const j = await asJson(r);
      if (!r.ok || (j && j.error)) throw new Error((j && j.error) || `HTTP ${r.status}`);
      setPlanPreview(j);
    } catch (e) {
      alert("Couldn't build the plan: " + String((e && e.message) || e));
    } finally { setPlanBusy(false); }
  };
  const confirmSendPlan = async () => {
    if (!manualFarmerId || planBusy) return;
    setPlanBusy(true);
    try {
      const r = await authedFetch(
        `/api/market?fn=jd-field-use-plan&farmer=${encodeURIComponent(manualFarmerId)}&year=2026`,
        { method: "POST" });
      const j = await asJson(r);
      if (!r.ok || (j && j.error)) throw new Error((j && j.error) || `HTTP ${r.status}`);
      // Say what was SENT, not just "sent" — a grower who mis-toggled a field needs
      // to see the number that left, while they still remember what they meant.
      // A send that did not actually mail says so: the grower has to know whether
      // the agent has it.
      const ok = j.emailed && j.emailed.sent !== false;
      setPlanSent(ok
        ? `Sent — ${fmt0(j.silage_acres)} ac silage / ${fmt0(j.grain_acres)} ac grain`
        : `Saved, but the email didn't go out — call the office`);
      setPlanPreview(null);
    } catch (e) {
      alert("Couldn't send the plan: " + String((e && e.message) || e));
    } finally { setPlanBusy(false); }
  };
  const setUse = async (op, use) => {
    // NO isPortal GUARD — the grower sets this one. `jd-field-use` resolves the
    // operation to the OWNER of the CLU and refuses anything off their own farms,
    // so the scope is enforced where it can't be clicked past.
    if (!op) return;
    // Every row already carries BOTH its grain and silage dates, so flip the
    // field's clock instantly in local state (no refetch, no re-sort lag) and
    // just persist in the background. On failure, reload to the server truth.
    setRows(rs => (rs || []).map(r => r.jd_operation_id === op ? { ...r, harvest_use: use } : r));
    setSavingUse(op);
    try {
      const r = await authedFetch(`/api/market?fn=jd-field-use&op=${encodeURIComponent(op)}&use=${use}`, { method: "POST" });
      const j = await asJson(r);
      if (!r.ok || (j && j.error)) throw new Error((j && j.error) || `HTTP ${r.status}`);
    } catch (e) { alert("Couldn't set grain/silage: " + String((e && e.message) || e)); setReloadKey(k => k + 1); }
    finally { setSavingUse(null); }
  };
  const fname = useMemo(() => { const m = {}; for (const f of farmers) if (f && f.id) m[f.id] = f.name; return m; }, [farmers]);
  // Grouped by the OWNER of the ground, not the operator of the machine — the row's
  // `farmer_id` is whoever ran the planter, so grouping on it would file a custom job
  // under the contractor. owner_farmer_id is what the server resolved from the CLU.
  const byFarm = useMemo(() => { const b = {}; for (const r of rows || []) { const k = r.owner_farmer_id || r.farmer_id; (b[k] || (b[k] = [])).push(r); } return b; }, [rows]);
  const farmOpts = useMemo(() => Object.keys(byFarm).map(id => ({ id, name: fname[id] || "—", n: byFarm[id].length })).sort((a, b) => String(a.name).localeCompare(String(b.name))), [byFarm, fname]);
  const farmRows = farmSel === "__all__" ? (rows || []) : (byFarm[farmSel] || []);
  // Hand entry is per FARM — it lists one grower's insured fields — so it needs a
  // single farm resolved. A portal user with one grant is that farm without picking.
  // Book farms that produced no projected fields — reachable only through the picker's
  // second group, and the reason that group exists.
  const otherFarms = useMemo(() => {
    const has = new Set(Object.keys(byFarm));
    return (farmers || []).filter(f => f && f.id && f.name && !has.has(f.id))
      .map(f => ({ id: f.id, name: f.name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [farmers, byFarm]);
  // The hand-entry rows print their own projection, from THESE rows — the same fetch
  // the table above draws, so there is no second query and no second answer. Keyed on
  // CLU because that is what a hand entry has (a manual row carries no JD field id).
  const projByClu = useMemo(() => {
    const m = {};
    for (const r of (rows || [])) {
      if (r.source !== "manual" || !r.clu_id) continue;
      m[String(r.clu_id).toLowerCase()] = r;
    }
    return m;
  }, [rows]);
  const manualFarmerId = farmSel !== "__all__" ? farmSel
    : ((restrictFarmerIds || []).filter(Boolean).length === 1 ? restrictFarmerIds.filter(Boolean)[0]
      : (farmOpts.length === 1 ? farmOpts[0].id : null));
  // THESE TWO BANNERS ARE ABOUT THE SELECTED FARM, NOT THE BOOK. The API accumulates
  // them across the caller's whole scope — the entire book for an admin — while the
  // FARM selector above filters rows in the browser, so the book-wide totals rendered
  // underneath a single grower and read as facts about them. Under LAWNEL FARMS 2 the
  // page claimed 731 ac of their ground was planted by PANEK FARMS; Lawnel planted all
  // 51 of their own fields and the two farms are nowhere near each other.
  const scopeNotTheirs = (farmSel !== "__all__" && notTheirs && notTheirs.by_farm)
    ? (notTheirs.by_farm[farmSel] || { foreign_fields: 0, foreign_acres: 0, other: [], custom_for: [] })
    : notTheirs;
  const shown = farmRows.filter(r =>
    (!reqRm || (!r.variety_unmapped && !!r.plant_date)) &&
    (!hideSmall || (Number(r.acres) || 0) > 3));
  const excluded = farmRows.length - shown.length;
  // Corn seedings the VIEW never returned, narrowed to whatever farm is selected.
  // A farm with nothing but dropped fields isn't IN the picker, so on "All farms"
  // this is the only place it is named at all.
  const dropMissing = useMemo(() => {
    const per = (dropped && dropped.by_farmer) || [];
    const mine = farmSel === "__all__" ? per : per.filter(f => f.farmer_id === farmSel);
    const total = mine.reduce((s, f) => s + (f.fields || 0), 0);
    const noBoundary = mine.reduce((s, f) => s + (f.no_boundary || 0), 0);
    // ⚠ THE SECOND CAUSE USED TO BE `total - noBoundary` — A RESIDUAL PRINTED AS A
    // MECHANISM. The sentence beside it said "has a boundary but no weather history
    // for their grid cell", which asserts a cell EXISTS. For every one of RED DAWN
    // FARMING LLC's 90 excluded corn fields there is no cell within 283 miles, so the
    // page was telling a Michigan grower the wrong reason his farm was missing — and
    // the two reasons need opposite fixes (build cells vs. backfill weather).
    //
    // Summed from the server's own per-farm counts now. A residual is still computed
    // as the FALLBACK for a payload written before those keys existed, so a stale
    // deploy degrades to the old single sentence instead of silently reporting zero.
    const offGrid = mine.reduce((s, f) => s + (f.off_grid || 0), 0);
    const noWeather = mine.reduce((s, f) => s + (f.no_weather || 0), 0);
    // ⚠ THE FOURTH STATE. Folded into off-grid it would tell a grower their ground
    // is outside the weather grid on the strength of a distance nobody could measure.
    const unknownWx = mine.reduce((s, f) => s + (f.unknown || 0), 0);
    const split = offGrid + noWeather + unknownWx > 0;
    const nearestMi = mine.reduce((m, f) => f.nearest_mi == null ? m
      : (m == null ? f.nearest_mi : Math.min(m, f.nearest_mi)), null);
    return {
      total, noBoundary, split,
      offGrid, noWeather, unknownWx, nearestMi,
      other: total - noBoundary,
      // Each farm names WHICH cause its fields fell to. One number per farm was the
      // conflation one level up: a farm listed under a weather sentence might have
      // been entirely boundary-less.
      farms: mine.map(f => {
        const parts = [];
        if (f.no_boundary) parts.push(`${f.no_boundary} no boundary`);
        if (f.off_grid) parts.push(`${f.off_grid} outside the grid`);
        if (f.no_weather) parts.push(`${f.no_weather} no weather yet`);
        if (f.unknown) parts.push(`${f.unknown} location not established`);
        return { name: fname[f.farmer_id] || "Unnamed farm", fields: f.fields,
                 detail: parts.length ? parts.join(", ") : String(f.fields) };
      }).slice(0, 8),
    };
  }, [dropped, farmSel, fname]);
  const fmt0 = (n) => Math.round(n || 0).toLocaleString();
  // Year-aware short date: "Oct 21", or "Feb 27 ’27" when it rolls into next year.
  const ymd = cmShortDate;   // one date format for this page — see cmShortDate
  const isNextYear = (d) => d && new Date(d + "T00:00:00").getFullYear() > 2026;
  const bl = (r) => cmRmUnknown(r) ? "—"
    : r.status === "mature" ? (r.black_layer_date ? "reached " + ymd(r.black_layer_date) : "reached")
    : ymd(r.black_layer_date);
  const hvCol = (r) => r["harvest_" + moist + "_date"];   // grain harvest at moisture target
  // ⚠ UNITS COME FROM THE OPERATION, NEVER FROM THE MODE. Grain reports bu/ac and
  // silage ton/ac (converted from the t/ha John Deere stores), and a field can be
  // chopped off a page showing the grain plan — so the label is whatever the combine
  // or the chopper actually did, not what this page expected it to do.
  const isHarvested = (r) => !!(r && r.harvested_date);
  const hvActual = (r) => {
    const bits = [ymd(r.harvested_date)];
    if (r.harvested_moisture != null) bits.push(r.harvested_moisture + "%");
    if (r.harvested_yield != null) bits.push(r.harvested_yield + " " + silageUnitLabel(r.harvested_unit || ""));
    return bits.join(" · ");
  };
  const hvText = (r) => isHarvested(r) ? hvActual(r) : cmRmUnknown(r) ? "—" : ymd(predFor(r));
  // Is this field on the SILAGE clock? In silage mode: all. In "plan" mode: only
  // the ones designated for chopping. In grain mode: none.
  // THE RECORD OUTRANKS THE INTENTION. `harvested_as` is read off the harvest
  // operation’s own crop (CORN SILAGE vs CORN) rather than guessed from a moisture
  // or a unit, so it states what the machine did. Until a field is harvested this is
  // exactly the old behaviour — the grower’s own toggle.
  const effUse = (r) => (r && r.harvested_as) ? r.harvested_as
    : (r && r.harvest_use === "silage") ? "silage" : "grain";
  // A chopped field is compared against the CHOP window, never the grain dry-down
  // date — ranging a 61% whole-plant moisture against a 23% grain target is not a
  // comparison, and it would poison the calibration those pairs exist to feed.
  const predFor = (r) => effUse(r) === "silage" ? r.silage_date : hvCol(r);
  const isSil = (r) => mode === "silage" || (mode === "plan" && effUse(r) === "silage");
  // The effective harvest event for the current mode — unifies grain & silage so
  // tiles, the curve and the table read one shape.
  const CFG = { ...CM_STATUS, ...CM_SIL };
  const evBase = (r) => cmRmUnknown(r)
    ? { type: isSil(r) ? "silage" : "grain", date: null, key: "unknown", cfg: CM_STATUS.unknown }
    : isSil(r)
    ? { type: "silage", date: r.silage_date, key: r.silage_status, cfg: CM_SIL[r.silage_status] || CM_SIL.sil_approaching, early: r.silage_early_date, late: r.silage_late_date }
    : { type: "grain", date: hvCol(r), key: r.status, cfg: CM_STATUS[r.status] || CM_STATUS.on_track };
  // A FIELD THAT IS OFF IS NO LONGER A MATURITY QUESTION, so the record outranks every
  // forecast state including "unknown" — a hybrid we cannot map still got combined, and
  // the combine is not an estimate. One precedence rule here, read by the tiles, the
  // status sort, the printed sheet and the export, because the screen having its own
  // copy of it is what let the print and the export keep showing the prediction.
  // ⚠ THE DATE IS LEFT ON THE PREDICTION. ev(r).date drives the readiness curve; moving
  // a harvested field onto its actual date would change what that graphic plots, which
  // is a decision and not a consequence. Every date returned here is unchanged.
  const ev = (r) => {
    const base = evBase(r);
    return isHarvested(r) ? { ...base, key: "harvested", cfg: CM_STATUS.harvested } : base;
  };
  // `unknown` sits LAST and is still a tile: a count that vanishes reads as a
  // book with nothing missing from it.
  const TILE_ORDER = ["harvested", "sil_ready", "sil_approaching", "sil_past", "mature", "on_track", "frost_risk", "wont_finish", "unknown"];
  const agg = (() => {
    const s = {}; let totF = 0, totAc = 0, unmapped = 0, silF = 0, silAc = 0, grainF = 0, grainAc = 0;
    for (const r of shown) { const k = ev(r).key; (s[k] || (s[k] = { f: 0, ac: 0 })); s[k].f++; s[k].ac += Number(r.acres) || 0; totF++; totAc += Number(r.acres) || 0; if (r.variety_unmapped) unmapped++;
      // ACRES, not just a field count. "12 fields marked silage" does not tell a
      // grower whether that is the back forty or half the farm, and the acre split
      // is what the chopper and the grain contract are both sized against.
      const ac = Number(r.acres) || 0;
      if (r.harvest_use === "silage") { silF++; silAc += ac; } else { grainF++; grainAc += ac; } }
    return { s, totF, totAc, unmapped, silF, silAc, grainF, grainAc };
  })();
  // COLUMN SORT. Every header sorts, including the ones that read as prose: Farm
  // and Variety are how you find a field whose name you already know, and Status
  // is how you pull the ones in trouble to the top. The default key reproduces
  // exactly what this table always did — soonest harvest/chop event first — so
  // nothing moves until a header is clicked.
  //
  // BLANKS ALWAYS LAND LAST, in BOTH directions. A field with no predicted date is
  // not an early one, and flipping the arrow must not promote a hole to the top of
  // the page — that is how a gap gets read as a finding. They fall back to the
  // default event order (not alphabetical), which also keeps the grain group sane
  // in "By plan" when the click landed on a silage-only column.
  const evKey = (r) => String(ev(r).date || "9");
  const SORT_COLS = {
    event:   (r) => ev(r).date,
    field:   (r) => (r.field_name || "").toLowerCase(),
    farm:    (r) => (fname[r.owner_farmer_id || r.farmer_id] || "").toLowerCase(),
    variety: (r) => (r.variety || "").toLowerCase(),
    use:     (r) => (effUse(r) === "silage" ? 0 : 1),
    planted: (r) => r.plant_date,
    pct:     (r) => (cmRmUnknown(r) || r.pct_to_maturity == null ? null : Math.min(100, r.pct_to_maturity)),
    // A mature field prints "reached" and carries no date — it IS the earliest
    // black layer, so it sorts as one rather than falling in with the unknowns.
    bl:      (r) => (cmRmUnknown(r) ? null
      : r.status === "mature" ? (r.black_layer_date || "0000-00-00")
      : r.black_layer_date),
    // Same rule as `bl` above, and for the same reason: before sql/059 lands these
    // columns still carry the 2,500-GDU fallback's dates, so sorting on them puts
    // an unknown field in among the dated ones as though its window were known.
    harvest: (r) => (cmRmUnknown(r) ? null : hvCol(r)),
    chopwin: (r) => (cmRmUnknown(r) ? null : r.silage_early_date),
    chop:    (r) => (cmRmUnknown(r) ? null : r.silage_date),
    // TILE_ORDER is the curated status order the roll-up tiles already use:
    // ready → approaching → past → mature → on track → frost risk → won't finish.
    status:  (r) => { const i = TILE_ORDER.indexOf(ev(r).key); return i < 0 ? TILE_ORDER.length : i; },
  };
  // The status tiles above are computed from `shown` (every status) and stay whole
  // while one is selected; only what's BELOW them narrows. Same key the tile is
  // drawn from — ev(r).key — so the count on the tile is the count you land on.
  // ⚠ A SPLIT PLANTER PUTS TWO HYBRIDS IN ONE FIELD, AND THIS TABLE NEVER SAID SO.
  // The view is one row per SEEDING PASS, so both varieties are already here — but the
  // default sort is by EVENT DATE, and two hybrids in one field mature on different
  // days by design, so their rows never land next to each other. Kevin, 2026-09-07:
  // "Meisenzahl Farms runs a lot of split planter and I only see one variety per field."
  // Both rows were on screen; nothing on either one said the field had another.
  //
  // Measured on the live 2026 book: 51 corn field-seasons carry more than one variety
  // (4.4%), and Meisenzahl alone has 61 seeding passes across 54 fields. His field 118
  // is P0035 AM Pivot BIO planted May 21 on 41.9 ac beside 194-04VT2PRIB planted Jun 2
  // on 15.4 ac — twelve days and a different hybrid, so genuinely two harvest dates.
  //
  // Keyed on the FIELD, not the field NAME: two growers can both have a "118", and a
  // manual planting carries no jd_field_id so it keys on its CLU instead.
  const splitFields = useMemo(() => {
    const byField = new Map();
    for (const r of shown) {
      const k = r.jd_field_id || r.clu_id || null;
      if (!k) continue;
      const key = (r.owner_farmer_id || r.farmer_id || "") + "|" + k;
      const v = String(r.variety || "").trim();
      if (!v) continue;
      const m = byField.get(key) || new Map();
      // ⚠ ACRES *AND* THE DATES. Kevin, seeing the badge: "Shouldn't there be 2
      // maturity dates per field if there are 2 varieties?" There are, and both
      // rows were already in the table — but sorted apart by event date, so the
      // badge naming the other hybrid still left him hunting for its dates. The
      // row now carries them.
      const prev = m.get(v) || { acres: 0, bl: null, mature: false };
      prev.acres += Number(r.acres) || 0;
      // First non-null wins: a variety drilled in two passes has one maturity,
      // and a later pass must not overwrite the date with a null.
      if (prev.bl == null) prev.bl = r.black_layer_date || null;
      if (r.status === "mature") prev.mature = true;
      m.set(v, prev);
      byField.set(key, m);
    }
    const out = new Map();
    for (const [key, m] of byField) if (m.size > 1) out.set(key, m);
    return out;
  }, [shown]);
  const splitOf = (r) => {
    const k = r.jd_field_id || r.clu_id || null;
    if (!k) return null;
    return splitFields.get((r.owner_farmer_id || r.farmer_id || "") + "|" + k) || null;
  };

  const visible = statusSel ? shown.filter(r => ev(r).key === statusSel) : shown;
  // The same status narrowing applied BEFORE the RM / small-field filters — this is
  // what the "not on this chart" notice counts from, so the notice stays scoped to
  // whatever the chart is now drawing rather than reporting the whole farm at it.
  const statusScoped = statusSel ? farmRows.filter(r => ev(r).key === statusSel) : farmRows;
  // Numbered over the farm's WHOLE row set, not the filtered one, so "Planting 2 of 2"
  // stays true when a small first planting is hidden by the ≤3 ac filter.
  const plantings = useMemo(() => cmPlantingsByField(farmRows), [farmRows]);
  const plantingOf = (r) => plantings.get(r.jd_operation_id) || null;
  const sorted = cmGroupPlantings((() => {
    const get = SORT_COLS[sortBy.key] || SORT_COLS.event;
    const dir = sortBy.dir === "desc" ? -1 : 1;
    const hole = (v) => v == null || v === "" || (typeof v === "number" && !Number.isFinite(v));
    const tie = (a, b) => evKey(a).localeCompare(evKey(b)) || String(a.field_name || "").localeCompare(String(b.field_name || ""));
    return [...visible].sort((a, b) => {
      const va = get(a), vb = get(b), ha = hole(va), hb = hole(vb);
      if (ha || hb) return ha && hb ? tie(a, b) : ha ? 1 : -1;
      const c = (typeof va === "number" && typeof vb === "number") ? (va - vb) : String(va).localeCompare(String(vb));
      return c ? c * dir : tie(a, b);
    });
  })(), plantings);
  const td = { padding: "8px 10px", fontSize: 12.5, borderBottom: "1px solid #eef2ee" };
  const cardBox = { background: "#fff", border: "1px solid #e4ece4", borderRadius: 12, padding: 16 };

  // Report rows for the current selection (respects the farm filter) in whatever
  // order the screen is currently sorted — the Excel and the print-out are the
  // table you are looking at, not a second opinion on how to order it.
  const mdate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
  const scope = farmSel === "__all__" ? "All connected farms" : (fname[farmSel] || "Farm");
  const exportXlsx = async () => {
    try {
      if (!window.XLSX) await new Promise((res, rej) => { const s = document.createElement("script"); s.src = "https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js"; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
      const XLSX = window.XLSX;
      const header = ["Farm", "Field", "Acres", "Variety", "RM (days)", "Planted", "Use", "% to maturity", "Black layer", `Grain harvest (≤${moist}%)`, "Silage ideal chop", "Silage window", "Harvested", "Harvest moisture %", "Harvest yield", "Yield unit", "First frost (est)", "Status"];
      const body = sorted.map(r => [
        fname[r.owner_farmer_id || r.farmer_id] || "—",
        (r.field_name || "—") + (plantingOf(r) ? ` (${cmPlantingLabel(plantingOf(r))})` : "") + (splitOf(r) ? ` (split · ${splitOf(r).size} varieties)` : ""),
        r.acres != null ? Number(r.acres) : "",
        r.variety || "", r.rm_days || "", r.plant_date || "",
        effUse(r) === "silage" ? "Silage" : "Grain",
        cmRmUnknown(r) || r.pct_to_maturity == null ? "" : Math.min(100, r.pct_to_maturity) / 100,
        cmRmUnknown(r) ? "" : (r.black_layer_date || (r.status === "mature" ? "reached" : "")),
        cmRmUnknown(r) ? "" : (hvCol(r) || ""), cmRmUnknown(r) ? "" : (r.silage_date || ""),
        cmRmUnknown(r) || !(r.silage_early_date && r.silage_late_date) ? "" : `${r.silage_early_date} → ${r.silage_late_date}`,
        r.harvested_date || "",
        r.harvested_moisture != null ? Number(r.harvested_moisture) : "",
        r.harvested_yield != null ? Number(r.harvested_yield) : "",
        r.harvested_date ? silageUnitLabel(r.harvested_unit || "") : "",
        r.frost_date || "", (ev(r).cfg || {}).label || ev(r).key,
      ]);
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      ws["!cols"] = [{ wch: 22 }, { wch: 20 }, { wch: 8 }, { wch: 22 }, { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 13 }, { wch: 13 }, { wch: 20 }, { wch: 16 }, { wch: 24 }, { wch: 15 }, { wch: 13 }];
      body.forEach((_, i) => { const c = ws[XLSX.utils.encode_cell({ r: i + 1, c: 7 })]; if (c && c.v !== "") c.z = "0%"; });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Corn Maturity 2026");
      XLSX.writeFile(wb, `Corn-Maturity-2026_${scope.replace(/[^\w]+/g, "-")}.xlsx`);
    } catch (e) { alert("Couldn't export Excel: " + String((e && e.message) || e)); }
  };
  const printReport = () => {
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const printDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    const tiles = TILE_ORDER.filter(k => agg.s[k]).map(k => { const v = agg.s[k]; const cfg = CFG[k]; return `<div class="tile" style="border-color:${cfg.c}44"><div class="tn" style="color:${cfg.c}">${v.f}</div><div class="tl" style="color:${cfg.c}">${esc(cfg.label)}</div><div class="ta">${fmt0(v.ac)} ac</div></div>`; }).join("");
    const midHead = mode === "silage" ? '<th class="r">Chop window</th><th class="r">Ideal chop</th>'
      : mode === "plan" ? '<th class="r">Black layer</th><th class="r">Harvest / Chop</th><th class="r">Chop window</th>'
      : `<th class="r">% mat.</th><th class="r">Black layer</th><th class="r">Harvest ≤${moist}%</th>`;
    const colCount = mode === "silage" ? 9 : 10;
    const rowsHtml = sorted.map(r => { const e = ev(r); const sil = r.harvest_use === "silage"; const useTxt = sil ? "Silage" : "Grain";
      const mid = mode === "silage" ? `<td class="r">${esc(ymd(r.silage_early_date))}–${esc(ymd(r.silage_late_date))}</td><td class="r" style="font-weight:700;color:${e.cfg.c}">${esc(hvText(r))}</td>`
        : mode === "plan" ? `<td class="r" style="font-weight:700">${sil ? "—" : esc(bl(r))}</td><td class="r" style="font-weight:700;color:${sil ? e.cfg.c : "#2d4a2d"}">${esc(hvText(r))}</td><td class="r">${sil ? esc(ymd(r.silage_early_date) + " – " + ymd(r.silage_late_date)) : "—"}</td>`
        : `<td class="r">${!cmRmUnknown(r) && r.pct_to_maturity != null ? Math.min(100, r.pct_to_maturity) + "%" : "—"}</td><td class="r" style="font-weight:700;color:${(cmRmUnknown(r) ? CM_STATUS.unknown : CM_STATUS[r.status] || {}).c || "#333"}">${esc(bl(r))}</td><td class="r" style="font-weight:700">${esc(hvText(r))}</td>`;
      return `<tr><td>${esc(fname[r.owner_farmer_id || r.farmer_id] || "—")}</td><td>${esc(r.field_name || "—")}${plantingOf(r) ? `<div style="font-size:9.5px;color:#7a5a10;font-weight:700">${esc(cmPlantingLabel(plantingOf(r)))}</div>` : ""}${splitOf(r) ? ` <span style="font-size:9px;color:#33507a;font-weight:700">SPLIT · ${splitOf(r).size}</span>` : ""}</td><td class="r">${r.acres != null ? Number(r.acres).toFixed(1) : "—"}</td><td>${esc(r.variety || "—")}${r.rm_days ? ` · ${r.rm_days}d` : ' · <span style="color:#a9791f">RM?</span>'}</td><td class="c">${useTxt}</td><td class="r">${esc(mdate(r.plant_date))}</td>${mid}<td class="c" style="color:${e.cfg.c};font-weight:700">${esc(e.cfg.label)}</td></tr>`; }).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Corn Maturity 2026 — ${esc(scope)}</title>
<style>
  body { font-family:'Helvetica Neue',Arial,sans-serif; color:#1a2e1a; padding:26px; max-width:1000px; margin:0 auto; }
  h1 { font-size:20px; margin:0 0 2px; } .sub { font-size:12.5px; color:#5c6f5c; } .asof { font-size:11px; color:#60735f; margin-bottom:14px; }
  .tiles { display:flex; gap:10px; margin:14px 0 18px; flex-wrap:wrap; }
  .tile { border:1px solid #ccc; border-radius:10px; padding:11px 15px; min-width:120px; }
  .tn { font-size:22px; font-weight:800; line-height:1; } .tl { font-size:12px; font-weight:700; margin-top:3px; } .ta { font-size:11px; color:#5c6f5c; margin-top:2px; }
  table { width:100%; border-collapse:collapse; font-size:11.5px; } th,td { padding:6px 9px; border-bottom:1px solid #eef2ee; text-align:left; }
  th { background:#f4f8f4; color:#5a7a5a; font-size:9.5px; text-transform:uppercase; letter-spacing:.04em; }
  td.r,th.r { text-align:right; font-variant-numeric:tabular-nums; }
  .print-btn { float:right; background:#2d5a10; color:#fff; border:none; border-radius:8px; padding:9px 16px; font-size:13px; font-weight:700; cursor:pointer; }
  .banner { background:#fff8ec; border:1px solid #f0dcae; border-radius:8px; padding:9px 13px; font-size:11px; color:#8a5a00; margin:12px 0; }
  .footer { margin-top:22px; padding-top:12px; border-top:1px solid #d6e0d6; font-size:10.5px; color:#60735f; }
  @media print { .no-print { display:none !important; } body { padding:0; } }
</style></head><body>
  <button class="print-btn no-print" onclick="window.print()">🖨️ Print / Save PDF</button>
  <h1>🌽 Corn ${mode === "silage" ? "Silage Chop" : mode === "plan" ? "Harvest Plan" : "Maturity"} — 2026</h1>
  <div class="sub">${esc(scope)} · ${mode === "silage" ? "predicted silage chop window (~35% whole-plant DM)" : mode === "plan" ? "each field on its grain or silage plan" : "predicted black layer + grain harvest per field"}</div>
  <div class="asof">Generated ${printDate} · ${sorted.length} field${sorted.length === 1 ? "" : "s"}, ${fmt0(agg.totAc)} ac</div>
  <div class="tiles">${tiles}</div>
  <div class="banner"><b>Estimate.</b> ${mode === "silage" ? "Silage chop = whole-plant dry-down to ~35% DM (65% moisture), ~900 GDU after silking / ~2 weeks before grain black layer." : "Heat units from plant date to today, then the 30-yr normal forward, to each hybrid's GDU-to-black-layer target; harvest = drydown past black layer to the grain-moisture target (a next-year date won't dry in-season). Fields marked Silage use the chop window."} Not a guarantee of harvest timing.</div>
  <table><thead><tr><th>Farm</th><th>Field</th><th class="r">Acres</th><th>Variety (RM)</th><th class="c">Use</th><th class="r">Planted</th>${midHead}<th>Status</th></tr></thead><tbody>${rowsHtml || `<tr><td colspan="${colCount}" style="color:#677167">No fields</td></tr>`}</tbody></table>
  <div class="footer">Clover Ag Risk Management · (585) 233-1066 · Corn maturity projection from John Deere machine data + WNY weather · ${printDate}</div>
  <script>window.addEventListener('load',function(){setTimeout(function(){try{window.print();}catch(e){}},500);});</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Popup blocked — allow popups for this site, then click again."); return; }
    writePrintWindow(w, html);
  };

  // One maturity table for a given field list + layout ('grain' or 'silage').
  // Grain layout shows % / black layer / harvest; silage shows chop window / ideal.
  // Used standalone for Grain & Silage modes, and twice (silage then grain) for By plan.
  const matTable = (list, layout) => (
    <div style={{ overflowX: "auto", border: "1px solid #eef2ee", borderRadius: 10 }}>
      <table style={{ width: "100%", minWidth: isMobile ? 620 : undefined, borderCollapse: "collapse", background: "#fff" }}>
        <thead><tr style={{ background: "#f6faf6" }}>
          {[{ k: "field", h: "Field", a: "left" },
            ...(farmSel === "__all__" ? [{ k: "farm", h: "Farm", a: "left" }] : []),
            { k: "variety", h: "Variety (RM)", a: "left" },
            { k: "use", h: "Use", a: "center" },
            { k: "planted", h: "Planted", a: "right" },
            ...(layout === "silage"
              ? [{ k: "chopwin", h: "Chop window", a: "right" }, { k: "chop", h: "Ideal chop", a: "right" }]
              : [{ k: "pct", h: "% to maturity", a: "right" }, { k: "bl", h: "Predicted black layer", a: "right" }, { k: "harvest", h: `Harvest ≤${moist}%`, a: "right" }]),
            { k: "status", h: "Status", a: "right" }].map(c => {
            const on = sortBy.key === c.k;
            return (
              <th key={c.k} onClick={() => setSortBy(s => ({ key: c.k, dir: s.key === c.k && s.dir === "asc" ? "desc" : "asc" }))}
                title={`Sort by ${c.h}`} aria-sort={on ? (sortBy.dir === "asc" ? "ascending" : "descending") : "none"}
                style={{ ...td, textAlign: c.a, fontSize: 10.5, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase",
                  color: on ? "#2d4a2d" : "#60735f", borderBottom: "2px solid " + (on ? "#2d4a2d" : "#cdd9cd"),
                  cursor: "pointer", userSelect: "none" }}>
                {c.h}<span style={{ marginLeft: 4, fontSize: 10, opacity: on ? 1 : 0.3 }}>{on ? (sortBy.dir === "asc" ? "▲" : "▼") : "▾"}</span>
              </th>
            );
          })}
        </tr></thead>
        <tbody>{list.map((r, i) => { const sil = r.harvest_use === "silage"; const cfg = isHarvested(r) ? CM_STATUS.harvested : cmRmUnknown(r) ? CM_STATUS.unknown : layout === "silage" ? (CM_SIL[r.silage_status] || CM_SIL.sil_approaching) : (CM_STATUS[r.status] || CM_STATUS.on_track); return (
          <tr key={r.jd_operation_id || i}>
            <td style={{ ...td, fontWeight: 700, color: "#1a2e1a" }}>{r.field_name || "—"}{r.acres ? <span style={{ color: "#677167", fontWeight: 400 }}> · {r.acres} ac</span> : null}
              {plantingOf(r) ? (
                <div title="This field was planted on more than one date. Each planting is estimated on its own date, so each has its own maturity."
                  style={{ fontSize: 10.5, fontWeight: 700, color: "#7a5a10", marginTop: 2 }}>
                  {cmPlantingLabel(plantingOf(r))}
                </div>
              ) : null}
              {(() => {
                // The badge names the OTHER hybrids and their acres, so the row is
                // self-contained wherever the sort puts it — the whole point, since
                // sorting by event date is what separated them.
                const sp = splitOf(r); if (!sp) return null;
                const others = [...sp.entries()].filter(([v]) => v !== String(r.variety || "").trim());
                const title = "Split planter — this field carries " + sp.size + " varieties: " +
                  [...sp.entries()].map(([v, d]) => v + " (" + d.acres.toFixed(1) + " ac, " +
                    (d.mature ? "black layer reached" : d.bl ? "black layer " + d.bl : "black layer unknown") + ")").join("; ") +
                  ". Each is estimated on its own hybrid and plant date, so they mature on different days.";
                return (
                  <span title={title} style={{ marginLeft: 7, fontSize: 10, fontWeight: 700, letterSpacing: ".03em",
                    background: "#eef3fb", color: "#33507a", border: "1px solid #cfdcef", borderRadius: 999, padding: "1px 7px", whiteSpace: "nowrap" }}>
                    SPLIT · {sp.size} varieties
                    {others.length === 1 ? (
                      <span style={{ fontWeight: 500 }}>
                        {" · also "}{others[0][0]}
                        {others[0][1].mature
                          ? <span style={{ color: "#1f5c2e" }}> · reached</span>
                          : others[0][1].bl
                            ? <span style={{ color: "#33507a" }}> · {others[0][1].bl}</span>
                            : null}
                      </span>
                    ) : null}
                  </span>
                );
              })()}
              {/* Their ground, someone else's planter. The row belongs here — the
                  acres are theirs — but it would read as a mistake unmarked. */}
              {r.custom_operated && <span title={`Planted by ${fname[r.operator_farmer_id] || "another operator"} — the ground is this grower's, so the field is listed here`}
                style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: ".04em", color: "#4a7a55", background: "#e6f2e8", border: "1px solid #bfe0c6", borderRadius: 999, padding: "1px 6px", verticalAlign: "middle" }}>CUSTOM</span>}</td>
            {farmSel === "__all__" && <td style={{ ...td, color: "#5c6f5c" }}>{fname[r.owner_farmer_id || r.farmer_id] || "—"}</td>}
            <td style={{ ...td }}>
              {edit && edit.op === r.jd_operation_id ? (
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <input autoFocus value={edit.variety} onChange={e2 => setEdit({ ...edit, variety: e2.target.value })} placeholder="Variety"
                    style={{ width: 140, fontSize: 12, padding: "4px 6px", border: "1px solid #cdd9cd", borderRadius: 6 }} />
                  <input value={edit.rm} onChange={e2 => setEdit({ ...edit, rm: e2.target.value.replace(/[^0-9]/g, "") })} placeholder="RM" inputMode="numeric"
                    title="Relative maturity in days (e.g. 98). Drives the black-layer target."
                    style={{ width: 52, fontSize: 12, padding: "4px 6px", border: "1px solid #cdd9cd", borderRadius: 6 }} />
                  <button onClick={saveVariety} disabled={saving} style={{ fontSize: 11, fontWeight: 700, padding: "4px 9px", background: "#2d4a2d", color: "#fff", border: "none", borderRadius: 6, cursor: saving ? "default" : "pointer" }}>{saving ? "…" : "Save"}</button>
                  <button onClick={() => setEdit(null)} disabled={saving} style={{ fontSize: 11, padding: "4px 6px", background: "transparent", color: "#60735f", border: "none", cursor: "pointer" }}>Cancel</button>
                </div>
              ) : (
                <span>
                  {r.variety || <span style={{ color: "#a9791f" }}>— no variety</span>}
                  {r.rm_days ? <span style={{ color: "#60735f" }}> · {r.rm_days}-day</span> : <span title="Variety not in the maturity table — using a regional default." style={{ color: "#a9791f", fontSize: 11 }}> · RM?</span>}
                  <button onClick={() => setEdit({ op: r.jd_operation_id, variety: r.variety || "", rm: r.rm_days || "" })}
                    title={isPortal ? "Correct the hybrid or add its relative maturity for this field" : "Add or edit variety / RM"}
                    style={{ marginLeft: 8, fontSize: 11, padding: "1px 6px", background: r.variety_unmapped ? "#fff3d6" : "#f2f6f2", color: r.variety_unmapped ? "#8a6400" : "#5c6f5c", border: "1px solid " + (r.variety_unmapped ? "#e8d9b4" : "#dbe6db"), borderRadius: 6, cursor: "pointer" }}>✎</button>
                </span>
              )}
            </td>
            <td style={{ ...td, textAlign: "center" }}>
              <span style={{ display: "inline-flex", border: "1px solid #cdd9cd", borderRadius: 7, overflow: "hidden", opacity: savingUse === r.jd_operation_id ? 0.5 : 1 }}
                title="Set this field's harvest use — silage fields group at the top, grain below">
                {[["grain", "Grain", "#2d4a2d"], ["silage", "Silage", "#2d8a4a"]].map(([u, lab, on], j) => {
                  const active = (sil ? "silage" : "grain") === u;
                  return (
                    // THE GROWER SETS THIS ONE (Kevin, 2026-08-16) — and the !isPortal
                    // guard that used to sit HERE is why it still read as broken three
                    // days later. The guard was lifted from setUse and from the server
                    // (jd-field-use is gateScoped, not requireAdmin) but never from the
                    // call site, so the button rendered live, clicked, and did nothing.
                    // LAWNEL FARMS 2 hit exactly that on 2026-08-19. Scope is enforced
                    // server-side against the CLU owner, which is where it cannot be
                    // clicked past — never by disabling the control and hoping.
                    <button key={u} onClick={() => { if (!active && savingUse !== r.jd_operation_id) setUse(r.jd_operation_id, u); }}
                      disabled={savingUse === r.jd_operation_id}
                      style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", border: "none", borderLeft: j ? "1px solid #cdd9cd" : "none", cursor: active ? "default" : "pointer", background: active ? on : "#fff", color: active ? "#fff" : "#60735f" }}>{lab}</button>
                  );
                })}
              </span>
            </td>
            <td style={{ ...td, textAlign: "right", color: "#5c6f5c" }}>{r.plant_date ? new Date(r.plant_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</td>
            {layout === "silage" ? <>
              <td style={{ ...td, textAlign: "right", color: "#5c6f5c", whiteSpace: "nowrap" }}>{ymd(r.silage_early_date)} – {ymd(r.silage_late_date)}</td>
              <td style={{ ...td, textAlign: "right", fontWeight: 700, color: cfg.c, whiteSpace: "nowrap" }}>
                {hvText(r)}
                {isHarvested(r) && !cmRmUnknown(r) && predFor(r) ? (
                  <div style={{ fontWeight: 400, fontSize: 10.5, color: "#60735f" }}>
                    model said {ymd(predFor(r))}
                  </div>) : null}</td>
            </> : <>
              <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#2d4a2d" }}>{!cmRmUnknown(r) && r.pct_to_maturity != null ? Math.min(100, r.pct_to_maturity) + "%" : "—"}</td>
              <td style={{ ...td, textAlign: "right", fontWeight: 700, color: (CM_STATUS[r.status] || CM_STATUS.on_track).c }}>{bl(r)}</td>
              <td style={{ ...td, textAlign: "right", fontWeight: 700, color: isNextYear(hvCol(r)) ? "#a9791f" : "#2d4a2d", whiteSpace: "nowrap" }} title={isNextYear(hvCol(r)) ? "Rolls into next year — won't dry to this moisture in-season" : undefined}>{hvText(r)}
                {isHarvested(r) && !cmRmUnknown(r) && predFor(r) ? (
                  <div style={{ fontWeight: 400, fontSize: 10.5, color: "#60735f" }}>
                    model said {ymd(predFor(r))}
                  </div>) : null}</td>
            </>}
            <td style={{ ...td, textAlign: "right" }}><span style={{ fontSize: 11, fontWeight: 800, color: cfg.c, background: cfg.bg, border: "1px solid " + cfg.c + "55", borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap" }}>{cfg.label}</span></td>
          </tr>
        ); })}</tbody>
      </table>
    </div>
  );
  const planSil = sorted.filter(r => r.harvest_use === "silage");
  const planGrain = sorted.filter(r => r.harvest_use !== "silage");
  const groupHdr = (label, color, n, ac) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "4px 2px 8px", borderLeft: "3px solid " + color, paddingLeft: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 800, color }}>{label}</span>
      <span style={{ fontSize: 11.5, color: "#60735f" }}>{n} field{n === 1 ? "" : "s"} · {fmt0(ac)} ac</span>
    </div>
  );

  if (err) return (
    <div style={{ ...cardBox, color: "#c0392b", fontSize: 13, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <span>Couldn't load corn maturity — {err}</span>
      <button onClick={() => { setErr(null); setRows(null); setReloadKey(k => k + 1); }}
        style={{ background: "#2d4a2d", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Try again</button>
    </div>
  );
  if (!rows) return <div style={{ ...cardBox, color: "#60735f", fontSize: 13 }}>Projecting corn maturity from plant date, variety &amp; this year's heat…</div>;
  // EMPTY IS THE CASE THIS FEATURE EXISTS FOR. A grower with no machine data has no
  // rows at all, so the hand-entry list must render HERE — the old early return sent
  // exactly the person who needs it to a dead end that named John Deere as the only
  // way in.
  // ⚠ THE EMPTY STATE IS WHERE THE EXPLANATION MATTERS MOST, AND IT WAS THE ONE
  // PLACE THAT DID NOT SHOW IT. This early return rendered the invitation and the
  // hand-entry list and skipped the drop note entirely — so a farm with NO
  // projectable field got two remedies and no reason, while the agent view (which
  // has rows from other farms and therefore never takes this branch) got the reason.
  //
  // Found by signing in as a real portal user. CHRIS DAWSON manages 8 operations;
  // on RED DAWN FARMING LLC he saw "Machine data fills this in on its own" directly
  // above a list reading "191 outside the weather grid — can't be projected". Red
  // Dawn HAS machine data — 365 CY2026 operations, a harvest the day before — and it
  // will never fill this in, because the nearest weather cell is 291 miles away.
  // Both remedies the page offered were impossible and neither said so.
  //
  // The panel is ONE definition rendered on both paths. Two copies of a sentence
  // this carefully worded is how the empty state came to disagree with the page.
  const dropNote = dropMissing.total > 0 ? (
            <div style={{ background: "#fff8ec", border: "1px solid #e8d9b4", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12.5, color: "#7a5a00", lineHeight: 1.5 }}>
              <b>{dropMissing.total} corn field{dropMissing.total === 1 ? "" : "s"} not on this page.</b>{" "}
              {dropMissing.noBoundary > 0 && <>{dropMissing.noBoundary} {dropMissing.noBoundary === 1 ? "has" : "have"} no field boundary from John Deere yet, so there is no location to run heat units against — they appear once the boundary sync reaches the farm (runs each morning; a new connection fills in on the next 3-hourly sync). </>}
              {dropMissing.split ? <>
                {dropMissing.offGrid > 0 && <>{dropMissing.offGrid} {dropMissing.offGrid === 1 ? "sits" : "sit"} outside the weather grid — {dropMissing.nearestMi != null ? <>the nearest cell is <b>{dropMissing.nearestMi} mi</b> away</> : "no cell is within reach"}, so there is no local heat history to run them against. Building cells for that ground is what would light them up. </>}
                {dropMissing.noWeather > 0 && <>{dropMissing.noWeather} {dropMissing.noWeather === 1 ? "is" : "are"} inside the grid but {dropMissing.noWeather === 1 ? "its" : "their"} cell carries no weather history yet. </>}
                {/* ⚠ SAYS LESS, ON PURPOSE. We hold a boundary but could not measure
                    where it is against the grid — so this names neither a distance nor
                    a weather gap, because either would be a claim we cannot support. */}
                {dropMissing.unknownWx > 0 && <>{dropMissing.unknownWx} {dropMissing.unknownWx === 1 ? "has" : "have"} a boundary whose location could not be established against the weather grid — we cannot say whether {dropMissing.unknownWx === 1 ? "it is" : "they are"} in reach. </>}
              </> : (
                /* Fallback for a payload written before the split shipped. Says LESS
                   rather than guessing which cause — a stale deploy must not start
                   asserting a mechanism this build can no longer stand behind. */
                dropMissing.other > 0 && <>{dropMissing.other} {dropMissing.other === 1 ? "has" : "have"} a boundary but no usable weather for {dropMissing.other === 1 ? "its" : "their"} location. </>
              )}
              {dropMissing.farms.length > 0 && <span style={{ color: "#8a7340" }}>{dropMissing.farms.map(f => `${f.name} (${f.detail})`).join(" · ")}</span>}
            </div>
  ) : null;
  // ⚠ NARROW ON PURPOSE: only the reasons we can PROVE are unprojectable count.
  // A missing BOUNDARY is genuinely fixable by the sync, so a selection merely
  // waiting on boundaries keeps the original invitation — withdrawing it there
  // would be the opposite error, telling a grower to give up on a farm that is
  // one sync away from working.
  const emptyCase = maturityEmptyCase(dropMissing);
  const allOffGrid = emptyCase === "all";
  const someOffGrid = emptyCase === "some";
  if (!rows.length) return (
    <div>
      <div style={{ ...cardBox, color: "#60735f", fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
        {allOffGrid
          ? <>No 2026 corn projection is possible for this selection. <b>Machine data cannot fill this in, and a hand-entered plant date cannot either</b> — the projection needs local weather for the field, and these fields have none. See below.</>
          : someOffGrid
          ? <>No 2026 corn projection for this selection. <b>{dropMissing.offGrid} of {dropMissing.total} corn fields cannot get one here</b> — they sit outside the weather grid, so neither machine data nor a hand-entered plant date can produce one for them.
            {dropMissing.noBoundary > 0 && <> {dropMissing.noBoundary} {dropMissing.noBoundary === 1 ? "has" : "have"} no field boundary from John Deere yet, so where {dropMissing.noBoundary === 1 ? "it sits" : "they sit"} is not known.</>}
            {dropMissing.noWeather > 0 && <> {dropMissing.noWeather} {dropMissing.noWeather === 1 ? "is" : "are"} within reach of the grid and waiting on weather history.</>}
            {dropMissing.unknownWx > 0 && <> {dropMissing.unknownWx} could not be measured against the grid.</>}
            {" "}See below.</>
          : <>No 2026 corn planting projected yet for this selection. Machine data fills this in on its own —
            or enter a plant date and hybrid by hand below and the projection runs the same way.</>}
      </div>
      {dropNote}
      {manualFarmerId && <ManualPlantingSection farmerId={manualFarmerId} farmerName={fname[manualFarmerId]} isMobile={isMobile} projByClu={projByClu} moist={moist} gridLimited={allOffGrid || someOffGrid} onChanged={() => setReloadKey(k => k + 1)} />}
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 12.5, color: "#60735f", marginBottom: 12, lineHeight: 1.5, maxWidth: 760 }}>
        {mode === "silage"
          ? <>Predicted <b>silage chop window</b> per field — whole-plant dry-down to ~35% DM (65% moisture), ~2 weeks before grain black layer. Chop <b style={{ color: CM_SIL.sil_ready.c }}>in-window</b> at 32–38% DM; past that it's <b style={{ color: CM_SIL.sil_past.c }}>getting dry</b>.</>
          : mode === "plan"
          ? <>Each field on <b>its own plan</b> — fields marked <b>Silage</b> use the silage chop window, the rest use grain black-layer + drydown. Set each field with the <b>Grain/Silage</b> toggle in the table.</>
          : <>Predicted <b>black layer</b> (physiological maturity) per field — heat units from the actual plant date to today, then the 30-yr normal forward, against each hybrid's maturity target. <b style={{ color: CM_STATUS.frost_risk.c }}>Frost risk</b> = won't black-layer before the ~Oct 12 killing frost.</>}
        {" "}John Deere doesn't always send a usable hybrid name — where you see <b style={{ color: "#a9791f" }}>RM?</b> or <b style={{ color: "#a9791f" }}>no variety</b>, click <b>✎</b> beside it to set the hybrid and its relative maturity. Nothing can be projected for a field without one.
      </div>

      {/* Grain / Silage / By-plan mode. Switching CLEARS a status tile selection:
          grain and silage use different status keys, so a selection made in one
          mode matches nothing in the other and would leave an empty table sitting
          under a tile row that says otherwise. */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {[["grain", "Grain"], ["silage", "Silage"], ["plan", "By plan"]].map(([m, lab]) => (
          <button key={m} onClick={() => { setMode(m); setStatusSel(null); }} title={m === "plan" ? "Each field on its designated grain/silage clock" : `Show every field on the ${m} clock`}
            style={{ fontSize: 12.5, fontWeight: 700, padding: "7px 14px", borderRadius: 8, border: "1px solid " + (mode === m ? "#2d4a2d" : "#cdd9cd"), background: mode === m ? "#2d4a2d" : "#f6faf6", color: mode === m ? "#fff" : "#2d4a2d", cursor: "pointer" }}>{lab}</button>
        ))}
      </div>

      {/* WHAT THE TOGGLES ADD UP TO. Acres, not a field count — "12 fields marked
          silage" doesn't say whether that's the back forty or half the farm, and
          the acre split is what the chopper and the grain contract are sized
          against. It sits above the table in every mode, because the toggles are
          set in the table and a total you have to scroll away from to read is a
          total nobody reads. */}
      {agg.totF > 0 && (
        <div style={{ display: "flex", gap: isMobile ? 10 : 18, alignItems: "center", flexWrap: "wrap",
                      marginBottom: 14, padding: "10px 14px", borderRadius: 10,
                      border: "1px solid #cdd9cd", background: "#f6faf6" }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "#5c6f5c" }}>
            Planned use
          </span>
          {[["Silage", agg.silF, agg.silAc, CM_SIL.sil_ready.c], ["Grain", agg.grainF, agg.grainAc, "#2d4a2d"]].map(([lab, f, ac, col]) => (
            <span key={lab} style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: col }}>{lab}</span>
              <b style={{ fontSize: 15, color: "#2d4a2d", fontVariantNumeric: "tabular-nums" }}>{fmt0(ac)}</b>
              <span style={{ fontSize: 11.5, color: "#60735f" }}>ac · {f} field{f === 1 ? "" : "s"}</span>
            </span>
          ))}
          <span style={{ fontSize: 11.5, color: "#60735f" }}>
            of {fmt0(agg.totAc)} ac shown
            {excluded > 0 && <span title="Fields hidden by the filters above are not counted here"> · {excluded} field{excluded === 1 ? "" : "s"} filtered out</span>}
          </span>

          {/* SEND PLAN — the grower's action, so portal only, and only once a single
              farm is resolved: a plan is per-farm and "all farms" has no owner to
              file it under. The server recomputes the split from the owner-scoped
              view rather than trusting these totals, and stores a SNAPSHOT, because
              the toggles keep moving after the message is sent. */}
          {isPortal && manualFarmerId && (
            <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10 }}>
              {planSent && <span style={{ fontSize: 11.5, fontWeight: 700, color: "#2f7d32" }}>{planSent}</span>}
              <button onClick={previewPlan} disabled={planBusy}
                title="Send this grain/silage split to Clover Ag"
                style={{ fontSize: 12.5, fontWeight: 700, padding: "7px 14px", borderRadius: 8,
                         border: "1px solid #2d4a2d", background: planBusy ? "#60735f" : "#2d4a2d",
                         color: "#fff", cursor: planBusy ? "default" : "pointer" }}>
                {planBusy ? "Working…" : "Send plan to agent"}
              </button>
            </span>
          )}
          {/* CONFIRMATION — what is about to be mailed, from the server's own dry run.
              Shown before anything is written, because sending files a permanent record
              and mails the office; there is no unsend. */}
          {planPreview && (
            <div style={{ position:"fixed", inset:0, zIndex:2000, background:"rgba(20,30,20,.45)",
                          display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
                 onClick={() => { if (!planBusy) setPlanPreview(null); }}>
              <div onClick={(ev) => ev.stopPropagation()}
                   style={{ background:"#fff", borderRadius:12, padding:isMobile?16:20, width:560, maxWidth:"96%",
                            maxHeight:"84vh", overflowY:"auto", boxShadow:"0 10px 40px rgba(20,40,20,.3)" }}>
                <div style={{ fontWeight:800, fontSize:16, color:"#1a2e1a" }}>Send this plan to your agent?</div>
                <div style={{ fontSize:12.5, color:"#60735f", marginTop:3 }}>
                  {planPreview.farmer || "Your farm"} · {planPreview.crop_year} · goes to {planPreview.would_email || "Clover Ag"}
                </div>

                <div style={{ display:"flex", gap:20, margin:"14px 0 10px", flexWrap:"wrap" }}>
                  <div><span style={{ fontSize:26, fontWeight:800, color:"#2d8a4a" }}>{fmt0(planPreview.silage_acres)}</span>
                    <span style={{ fontSize:12, color:"#60735f", marginLeft:5 }}>ac silage · {planPreview.silage_fields} field{planPreview.silage_fields===1?"":"s"}</span></div>
                  <div><span style={{ fontSize:26, fontWeight:800, color:"#2d4a2d" }}>{fmt0(planPreview.grain_acres)}</span>
                    <span style={{ fontSize:12, color:"#60735f", marginLeft:5 }}>ac grain · {planPreview.grain_fields} field{planPreview.grain_fields===1?"":"s"}</span></div>
                </div>

                {/* Both directions of a change of intent. Each needs the crop appraised
                    BEFORE the field is taken, so they are named here and not only in
                    the agent's copy — the grower is the one standing next to the field. */}
                {!!(planPreview.change_of_intent && planPreview.change_of_intent.grain_insured_being_chopped.fields) && (
                  <div style={{ padding:"9px 11px", borderLeft:"4px solid #a9791f", background:"#fdf6e8",
                                borderRadius:5, fontSize:12.5, lineHeight:1.5, color:"#6a5a3a", marginBottom:8 }}>
                    <b style={{ color:"#7a5a10" }}>{planPreview.change_of_intent.grain_insured_being_chopped.fields} field{planPreview.change_of_intent.grain_insured_being_chopped.fields===1?"":"s"} insured as grain ({fmt0(planPreview.change_of_intent.grain_insured_being_chopped.acres)} ac) {planPreview.change_of_intent.grain_insured_being_chopped.fields===1?"is":"are"} marked for chopping.</b>{" "}
                    That has to be reported and the crop appraised before it's chopped — your agent is being told with this plan.
                  </div>
                )}
                {!!(planPreview.change_of_intent && planPreview.change_of_intent.silage_insured_not_being_chopped.fields) && (
                  <div style={{ padding:"9px 11px", borderLeft:"4px solid #c0392b", background:"#fdecec",
                                borderRadius:5, fontSize:12.5, lineHeight:1.5, color:"#7a2e26", marginBottom:8 }}>
                    <b style={{ color:"#c0392b" }}>{planPreview.change_of_intent.silage_insured_not_being_chopped.fields} field{planPreview.change_of_intent.silage_insured_not_being_chopped.fields===1?"":"s"} insured as silage ({fmt0(planPreview.change_of_intent.silage_insured_not_being_chopped.acres)} ac) {planPreview.change_of_intent.silage_insured_not_being_chopped.fields===1?"is":"are"} NOT marked for chopping.</b>{" "}
                    Combining those for grain has to be reported and appraised first too.
                  </div>
                )}
                {planPreview.not_answered_fields > 0 && (
                  <div style={{ padding:"8px 10px", background:"#f2f6f2", borderLeft:"4px solid #60735f",
                                borderRadius:5, fontSize:12.5, lineHeight:1.5, color:"#4a5a4a", marginBottom:8 }}>
                    {planPreview.not_answered_fields} field{planPreview.not_answered_fields===1?"":"s"} ({fmt0(planPreview.not_answered_acres)} ac) have no answer either way yet — they'll be listed as undecided.
                  </div>
                )}

                {/* The rows themselves. A grower confirming a number they cannot see is
                    not confirming anything. */}
                {(() => {
                  const rows = ((planPreview.fields && planPreview.fields.silage) || []);
                  if (!rows.length) return (
                    <div style={{ fontSize:12.5, color:"#60735f", padding:"6px 0" }}>No fields are marked for silage — this plan says the whole farm stays grain.</div>
                  );
                  return (
                    <div style={{ maxHeight:200, overflowY:"auto", border:"1px solid #e6ede6", borderRadius:7 }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
                        <thead><tr style={{ background:"#f2f6f2", position:"sticky", top:0 }}>
                          <th style={{ textAlign:"left", padding:"6px 8px", color:"#54724f" }}>Farm</th>
                          <th style={{ textAlign:"left", padding:"6px 8px", color:"#54724f" }}>Tract</th>
                          <th style={{ textAlign:"left", padding:"6px 8px", color:"#54724f" }}>Field</th>
                          <th style={{ textAlign:"right", padding:"6px 8px", color:"#54724f" }}>Acres</th>
                        </tr></thead>
                        <tbody>{rows.map((f, ix) => (
                          <tr key={f.key || ix} style={{ borderTop:"1px solid #eef2ee" }}>
                            <td style={{ padding:"5px 8px", fontWeight:700, color:"#1a2e1a" }}>{f.farm || f.field_name || "—"}</td>
                            <td style={{ padding:"5px 8px", color:"#54724f" }}>{f.tract || "—"}</td>
                            <td style={{ padding:"5px 8px", color:"#54724f" }}>{f.field || "—"}</td>
                            <td style={{ padding:"5px 8px", textAlign:"right", color:"#1a2e1a" }}>{f.acres != null ? Number(f.acres).toFixed(1) : "—"}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  );
                })()}

                <div style={{ display:"flex", gap:8, marginTop:14, flexWrap:"wrap" }}>
                  <button onClick={() => setPlanPreview(null)} disabled={planBusy}
                    style={{ padding:"10px 14px", border:"1px solid #cdd9cd", borderRadius:8, background:"#fff",
                             color:"#2d4a2d", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                    Go back and change it
                  </button>
                  <button onClick={confirmSendPlan} disabled={planBusy}
                    style={{ flex:1, minWidth:170, padding:"10px 14px", border:"none", borderRadius:8,
                             background: planBusy ? "#60735f" : "#2d6a1a", color:"#fff", fontWeight:800,
                             fontSize:13.5, cursor: planBusy ? "default" : "pointer", fontFamily:"inherit" }}>
                    {planBusy ? "Sending…" : "Yes, send it"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Farm selector — All connected farms + drill to one */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "#5c6f5c" }}>Farm</span>
        <select value={farmSel} onChange={e => setFarmSel(e.target.value)}
          style={{ fontSize: 13, fontWeight: 700, padding: "7px 10px", borderRadius: 8, border: "1px solid #cdd9cd", background: "#fff", color: "#2d4a2d", cursor: "pointer", maxWidth: 320 }}>
          <option value="__all__">All farms with data ({farmOpts.length})</option>
          {farmOpts.map(o => <option key={o.id} value={o.id}>{o.name} · {o.n} field{o.n === 1 ? "" : "s"}</option>)}
          {/* A farm with NO projected fields cannot be reached from a picker built out
              of the projection — which is precisely the farm that needs hand entry.
              Agent-side only: a portal user has their own farm and no list to pick from. */}
          {!isPortal && otherFarms.length > 0 && (
            <optgroup label="No machine data — enter by hand">
              {otherFarms.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </optgroup>
          )}
        </select>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
          {mode !== "silage" && <>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "#5c6f5c" }}>Harvest at</span>
          <select value={moist} onChange={e => setMoist(e.target.value)} title="Grain-moisture target for the estimated harvest date"
            style={{ fontSize: 12.5, fontWeight: 700, padding: "6px 8px", borderRadius: 8, border: "1px solid #cdd9cd", background: "#fff", color: "#2d4a2d", cursor: "pointer" }}>
            <option value="25">25%</option>
            <option value="23">23% · harvest start</option>
            <option value="20">20%</option>
            <option value="15">15% · storage-dry</option>
          </select>
          </>}
          <button onClick={printReport} title="Print or save the report as a PDF"
            style={{ fontSize: 12, fontWeight: 700, padding: "7px 12px", borderRadius: 8, border: "1px solid #cdd9cd", background: "#f6faf6", color: "#2d4a2d", cursor: "pointer" }}>🖨 Print</button>
          <button onClick={exportXlsx} title="Download this report as an Excel workbook"
            style={{ fontSize: 12, fontWeight: 700, padding: "7px 12px", borderRadius: 8, border: "1px solid #cdd9cd", background: "#f6faf6", color: "#2d4a2d", cursor: "pointer" }}>⤓ Excel</button>
        </div>
      </div>

      {/* Hand entry, once a single farm is in view — it lists THAT grower's insured
          fields, so it has nothing to show against "all farms". */}
      {manualFarmerId && <ManualPlantingSection farmerId={manualFarmerId} farmerName={fname[manualFarmerId]} isMobile={isMobile} projByClu={projByClu} moist={moist} gridLimited={allOffGrid || someOffGrid} onChanged={() => setReloadKey(k => k + 1)} />}

      {/* Filters */}
      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "#5c6f5c" }}>Filters</span>
        {[["Require RM + plant date", reqRm, setReqRm], ["Hide fields ≤ 3 ac", hideSmall, setHideSmall]].map(([lab, val, set], i) => (
          <label key={i} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12.5, color: "#3a4f3a", cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} style={{ accentColor: "#2d6a2d", cursor: "pointer", width: 15, height: 15 }} />
            {lab}
          </label>
        ))}
        {excluded > 0 && <span style={{ fontSize: 11.5, color: "#677167" }}>{excluded} field{excluded === 1 ? "" : "s"} hidden</span>}
        {/* A variety edit can move MORE than the row you clicked, so the receipt
            says how many. Silent bulk writes are how you find out later. */}
        {savedNote && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#1f7a3d", background: "#e6f2e8", border: "1px solid #bfe0c6", borderRadius: 999, padding: "3px 11px" }}>
            ✓ {savedNote}
            <button onClick={() => setSavedNote(null)} title="Dismiss"
              style={{ marginLeft: 7, border: "none", background: "transparent", color: "#4a7a55", cursor: "pointer", fontSize: 12, padding: 0, lineHeight: 1 }}>×</button>
          </span>
        )}
      </div>

      {/* CORN THIS PAGE COULD NOT PLACE. Distinct from "hidden" above, which counts
          rows the FILTERS dropped — these never arrived. The maturity view needs a
          field boundary to pick a weather grid cell, so a farm John Deere has sent
          us plantings for but no boundaries for is absent from the table AND from
          the farm picker, with nothing on the page saying so. That is how five
          farms' corn went missing on 2026-08-13. Say it, and name them. */}
      {/* CUSTOM WORK, SET ASIDE. Distinct again from both counts above: these fields
          charted fine, they are just not this grower's ground — their boundary lands
          on no insured CLU of theirs. The two lines say different things on purpose:
          one NAMES the owner because the field overlaps THAT insured's CLU, the other
          admits we know only that it isn't theirs. Collapsing them into "hidden" would
          claim knowledge we don't have. */}
      {scopeNotTheirs && (scopeNotTheirs.foreign_fields > 0 || (scopeNotTheirs.other || []).length > 0) && (
        <div style={{ background: "#f2f6fb", border: "1px solid #ccdcea", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12.5, color: "#3c5772", lineHeight: 1.5 }}>
          <b>Custom work is not on this page.</b>{" "}
          {scopeNotTheirs.foreign_fields > 0 && <>{scopeNotTheirs.foreign_fields} field{scopeNotTheirs.foreign_fields === 1 ? "" : "s"} ({fmt0(scopeNotTheirs.foreign_acres)} ac) sit on no insured CLU of this grower's — planted for someone we don't insure, so we can say it isn't theirs but not whose. </>}
          {(scopeNotTheirs.other || []).map(o => (
            <span key={o.farmer_id || o.name}>{o.fields} field{o.fields === 1 ? "" : "s"} ({fmt0(o.acres)} ac) belong to <b>{o.name}</b> and read on their page. </span>
          ))}
        </div>
      )}

      {/* THE OTHER SIDE OF THE SAME RELATIONSHIP. Machine data follows the CLU, so a
          field someone ELSE planted on this grower's ground is on this page and in
          these totals. Nobody should have to wonder why a field they never drove is
          listed, so name who ran it. */}
      {scopeNotTheirs && (scopeNotTheirs.custom_for || []).length > 0 && (
        <div style={{ background: "#f4f9f4", border: "1px solid #cfe3cf", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12.5, color: "#33553a", lineHeight: 1.5 }}>
          <b>Planted for this grower by someone else.</b>{" "}
          {(scopeNotTheirs.custom_for || []).map(o => (
            <span key={o.farmer_id || o.name}>{o.fields} field{o.fields === 1 ? "" : "s"} ({fmt0(o.acres)} ac) on this grower's ground were run by <b>{o.name}</b>'s machines. </span>
          ))}
          The acres are this grower's, so they are counted here.
        </div>
      )}

      {dropNote}

      {/* Status roll-up — grain statuses, silage chop statuses, or a mix (By plan).
          EACH TILE IS A FILTER: click to show only those fields, click again to
          clear. "82 frost risk" is the number you act on, so the fields behind it
          have to be one click away. The tiles keep counting the WHOLE selection
          while one is active — a tile row that zeroed itself on click would erase
          the comparison it exists to make — so the active tile is marked instead. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: statusSel ? 10 : 18 }}>
        {TILE_ORDER.filter(k => agg.s[k]).map(k => {
          const v = agg.s[k]; const cfg = CFG[k]; const on = statusSel === k;
          return (
            <button key={k} onClick={() => setStatusSel(s => s === k ? null : k)}
              title={on ? `Showing only ${cfg.label} — click to show all` : `Show only the ${v.f} ${cfg.label} field${v.f === 1 ? "" : "s"}`}
              style={{ textAlign: "left", font: "inherit", cursor: "pointer", background: cfg.bg,
                border: (on ? "2px solid " : "1px solid ") + (on ? cfg.c : cfg.c + "44"), borderRadius: 10,
                padding: on ? "12px 14px" : "13px 15px", boxShadow: on ? `0 0 0 3px ${cfg.c}22` : "none" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: cfg.c, fontFamily: "'Lora',serif", lineHeight: 1 }}>{v.f}</div>
              <div style={{ fontSize: 12, color: cfg.c, fontWeight: 700, marginTop: 4 }}>{cfg.label}{on ? " ✓" : ""}</div>
              <div style={{ fontSize: 11.5, color: "#5c6f5c", marginTop: 2 }}>{fmt0(v.ac)} ac</div>
            </button>
          );
        })}
      </div>
      {/* Say what is being looked at. Everything below — the table, the readiness
          curve, the Excel and the print-out — is the filtered set, so the filter
          cannot be a subtle border on one tile and nothing else. */}
      {statusSel && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 18, fontSize: 12.5, color: "#3a4f3a" }}>
          <span>Showing <b>{(CFG[statusSel] || {}).label || statusSel}</b> only — {sorted.length} field{sorted.length === 1 ? "" : "s"}, {fmt0(sorted.reduce((s, r) => s + (Number(r.acres) || 0), 0))} ac. The chart, Excel and print-out follow this filter.</span>
          <button onClick={() => setStatusSel(null)}
            style={{ fontSize: 12, fontWeight: 700, padding: "4px 11px", borderRadius: 999, border: "1px solid #cdd9cd", background: "#f6faf6", color: "#2d4a2d", cursor: "pointer" }}>✕ Show all statuses</button>
        </div>
      )}

      {/* Harvest-readiness bell curve + "% of acres ready by a date" explorer.
          Acres-weighted; RM-unmapped fields excluded (Kevin's ask). Drag the
          slider to see how much of the crop is dry to ≤moist% by any date. */}
      {(() => {
        const doy = (d) => { const dt = new Date(d + "T00:00:00"); return Math.round((dt - new Date(2026, 0, 0)) / 86400000); };
        const dOfY = (n) => new Date(2026, 0, n).toLocaleDateString("en-US", { month: "short", day: "numeric" }) + (n > 365 ? " ’27" : "");
        const evLabel = mode === "silage" ? "Chop window" : mode === "plan" ? "Harvest + chop plan" : "Harvest window";
        const evSub = mode === "silage" ? "acres ready to chop (~35% DM)" : mode === "plan" ? "each field on its designated plan" : `acres ready at ≤${moist}%`;
        const allPts = visible.filter(r => !r.variety_unmapped && ev(r).date).map(r => ({ t: doy(ev(r).date), w: Math.max(0.1, Number(r.acres) || 0.1) }));
        // COUNTED OFF statusScoped, NOT `shown`/`visible`. Those have ALREADY
        // dropped the RM-unmapped fields whenever the RM filter is on, so counting
        // from them returned 0 in exactly the case the notice exists to report — the
        // chart
        // read "100% of acres ready · 597 of 597 ac · 14/14 fields" while 37 of
        // Lawnel's 51 corn fields and 1,083.7 of its 1,680.6 acres were not on the
        // chart at all. The same defect the view-level drop counter already carries
        // a comment about: a count taken after the filter can only ever report the
        // rows that survived it. Acres come along too, because "37 fields" does not
        // tell a grower how much of the farm is missing.
        const rmMissing = statusScoped.filter(r => r.variety_unmapped);
        const noRm = rmMissing.length;
        const noRmAc = rmMissing.reduce((s, r) => s + (Number(r.acres) || 0), 0);
        const chartCard = { background: "#fff", border: "1px solid #e4ece4", borderRadius: 12, padding: isMobile ? 12 : 16, marginBottom: 16 };
        if (allPts.length < 3) return (
          <div style={{ ...chartCard, fontSize: 12.5, color: "#60735f" }}>Not enough mapped fields to chart the {mode === "silage" ? "chop" : "harvest"} window{mode === "silage" ? "" : ` at ≤${moist}%`}.</div>
        );
        const totAc = allPts.reduce((s, p) => s + p.w, 0);
        // Fields whose date rolls past this calendar year (won't get there in-season).
        const curvePts = allPts.filter(p => p.t <= 365);
        const later = allPts.filter(p => p.t > 365); const laterAc = later.reduce((s, p) => s + p.w, 0);
        const bw = 6; // Gaussian bandwidth, days
        // ⚠ THE KERNEL MUST NOT PUT ACRES WHERE THERE ARE NONE. A Gaussian reaches two
        // or three bandwidths either side of every point, so with the axis padded ten
        // days before the earliest field the curve had real height — and real shading —
        // over a stretch where not one acre is ready. Measured at 11.2% of the drawn
        // area on the farm that prompted this, beside a headline of 0%.
        const firstT = Math.min(...curvePts.map(p => p.t));
        const lastT = Math.max(...curvePts.map(p => p.t));
        const minT = Math.max(196, firstT - 10), maxT = 366;
        const N = 180, xs = Array.from({ length: N + 1 }, (_, i) => minT + (maxT - minT) * i / N);
        // Zero outside the range the fields occupy, so the shaded area can only ever
        // cover days on which acres genuinely come ready. The headline stays the
        // authority — it counts real fields — and the picture now agrees with it.
        const dens = xs.map(x => (x < firstT || x > lastT) ? 0
          : curvePts.reduce((s, p) => s + p.w * Math.exp(-0.5 * ((x - p.t) / bw) ** 2), 0));
        const maxD = Math.max(...dens, 1e-9);
        let peakI = 0; dens.forEach((d, i) => { if (d > dens[peakI]) peakI = i; });
        const peakT = Math.round(xs[peakI]);
        const cut = Math.max(minT, Math.min(maxT, cutoff));
        const readyAc = allPts.filter(p => p.t <= cut).reduce((s, p) => s + p.w, 0);
        const readyN = allPts.filter(p => p.t <= cut).length;
        const readyPct = Math.round(100 * readyAc / totAc);
        const W = 720, H = isMobile ? 200 : 250, PAD = { l: 14, r: 14, t: 22, b: 30 };
        const xOf = (t) => PAD.l + (W - PAD.l - PAD.r) * (t - minT) / (maxT - minT);
        const yOf = (d) => PAD.t + (H - PAD.t - PAD.b) * (1 - d / maxD);
        const lineP = "M" + xs.map((x, i) => `${xOf(x).toFixed(1)},${yOf(dens[i]).toFixed(1)}`).join("L");
        const areaP = `M${xOf(xs[0]).toFixed(1)},${yOf(0).toFixed(1)}L` + xs.map((x, i) => `${xOf(x).toFixed(1)},${yOf(dens[i]).toFixed(1)}`).join("L") + `L${xOf(xs[N]).toFixed(1)},${yOf(0).toFixed(1)}Z`;
        const readyXs = xs.filter(x => x <= cut);
        const readyP = readyXs.length ? `M${xOf(readyXs[0]).toFixed(1)},${yOf(0).toFixed(1)}L` + readyXs.map(x => `${xOf(x).toFixed(1)},${yOf(dens[xs.indexOf(x)]).toFixed(1)}`).join("L") + `L${xOf(readyXs[readyXs.length - 1]).toFixed(1)},${yOf(0).toFixed(1)}Z` : "";
        const y0 = yOf(0);
        const monthTicks = [213, 244, 274, 305, 335, 365].filter(n => n >= minT && n <= maxT);
        return (
          <div style={chartCard}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>🌽 {evLabel} <span style={{ color: "#60735f", fontWeight: 600, fontSize: 12.5 }}>· {evSub}</span></div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#2d6a2d" }}>Peak ~{dOfY(peakT)}</div>
            </div>
            {/* Ready-by-date explorer */}
            <div style={{ background: "#f6faf6", border: "1px solid #e4ece4", borderRadius: 10, padding: "10px 13px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#1f5d22", fontFamily: "'Lora',serif", lineHeight: 1 }}>{readyPct}%</div>
                <div style={{ fontSize: 12.5, color: "#3a4f3a" }}>of acres ready by <b>{dOfY(cut)}</b> <span style={{ color: "#60735f" }}>· {fmt0(readyAc)} of {fmt0(totAc)} ac · {readyN}/{allPts.length} fields</span></div>
              </div>
              <input type="range" min={minT} max={maxT} value={cut} onChange={e => setCutoff(Number(e.target.value))}
                style={{ width: "100%", marginTop: 8, accentColor: "#2d6a2d", cursor: "pointer" }} />
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ width: "100%", height: H, display: "block" }} preserveAspectRatio="none">
              <defs>
                <linearGradient id="hcFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3f9142" stopOpacity="0.16" />
                  <stop offset="100%" stopColor="#3f9142" stopOpacity="0.01" />
                </linearGradient>
              </defs>
              {monthTicks.map((n, i) => (
                <g key={i}>
                  <line x1={xOf(n)} y1={PAD.t - 6} x2={xOf(n)} y2={y0} stroke="#eef2ee" strokeWidth="1" />
                  <text x={xOf(n) + 3} y={y0 + 18} fontSize="10.5" fill="#677167" fontWeight="700">{dOfY(n)}</text>
                </g>
              ))}
              <line x1={PAD.l} y1={y0} x2={W - PAD.r} y2={y0} stroke="#cdd9cd" strokeWidth="1" />
              <path d={areaP} fill="url(#hcFill)" />
              {readyP && <path d={readyP} fill="#3f9142" fillOpacity="0.34" />}
              <path d={lineP} fill="none" stroke="#1f5d22" strokeWidth="2.2" strokeLinejoin="round" />
              <line x1={xOf(cut)} y1={PAD.t - 6} x2={xOf(cut)} y2={y0} stroke="#1a5a1a" strokeWidth="2" />
              <circle cx={xOf(cut)} cy={PAD.t - 6} r="3.5" fill="#1a5a1a" />
              <line x1={xOf(peakT)} y1={yOf(dens[peakI])} x2={xOf(peakT)} y2={y0} stroke="#2d6a2d" strokeWidth="1.3" strokeDasharray="3 3" />
            </svg>
            <div style={{ fontSize: 11, color: "#60735f", marginTop: 4, lineHeight: 1.5 }}>
              Shaded = the {mode === "silage" ? "chop" : "harvest"} window up to the line, smoothed over 6 days to show when the rush falls — <b>the exact acres are the figure above</b>, counted field by field. Drag to explore. {later.length > 0 && <span><b style={{ color: "#a9791f" }}>{later.length} field{later.length === 1 ? "" : "s"} ({fmt0(laterAc)} ac) {mode === "silage" ? "not ready to chop" : `won't dry to ${moist}%`} this season</b> (roll into next year). </span>}{noRm > 0 && <span><b style={{ color: "#a9791f" }}>{noRm} field{noRm === 1 ? "" : "s"} ({fmt0(noRmAc)} ac) are NOT on this chart</b> — their variety has no relative-maturity mapping, so there is nothing to project. The percentage above is of charted acres only. Add the hybrid's RM to price it in.</span>}
            </div>
          </div>
        );
      })()}

      {mode === "grain" ? matTable(sorted, "grain")
        : mode === "silage" ? matTable(sorted, "silage")
        : <>
            {planSil.length > 0 && <div>
              {groupHdr("Silage", "#2d8a4a", planSil.length, planSil.reduce((s, r) => s + (Number(r.acres) || 0), 0))}
              {matTable(planSil, "silage")}
            </div>}
            {planGrain.length > 0 && <div style={{ marginTop: planSil.length ? 18 : 0 }}>
              {groupHdr("Grain", "#2d4a2d", planGrain.length, planGrain.reduce((s, r) => s + (Number(r.acres) || 0), 0))}
              {matTable(planGrain, "grain")}
            </div>}
          </>}
      <div style={{ fontSize: 11.5, color: "#60735f", marginTop: 10, lineHeight: 1.5 }}>
        {mode === "silage"
          ? <><b>Chop window</b> = whole-plant dry-down to 32% (early/wet) → 38% DM (getting dry); <b>ideal ~35% DM</b> (65% moisture), roughly 900 GDU after silking. Silage runs ~2 weeks ahead of grain black layer.</>
          : mode === "plan"
          ? <>Flip any field's <b>Grain/Silage</b> toggle to move it between the two groups. <b>Silage</b> fields (top) show the chop window (~35% DM); <b>grain</b> fields (below) show black layer + harvest ≤{moist}%.</>
          : <><b>Harvest ≤{moist}%</b> = the date the field drydowns to that grain moisture (from ~30% at black layer, warm-rate + cold-weather floor). A <b style={{ color: "#a9791f" }}>next-year date</b> means it won't dry that far in-season — combine wet &amp; dry mechanically.</>}
      </div>
      {agg.unmapped > 0 && <div style={{ fontSize: 11.5, color: "#a9791f", marginTop: 8 }}>⚠ {agg.unmapped} field{agg.unmapped === 1 ? "" : "s"} on a variety not yet in the maturity table — shown on a regional default (RM?). Map them for an exact call.</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WINTER WHEAT PLANTING — book-wide progress for the crop going in the ground now.
//
// Everything on this panel comes from ONE endpoint (`fn=jd-wheatplanting`) whose
// rules live in api/_wheat-progress.js. Nothing is re-derived here: a second copy
// of "what does this farm usually plant" is how two screens come to disagree about
// one farm, which this repo has paid for more than once.
//
// The design follows what the data actually supports, measured 2026-09-15:
//   · the denominator is the farm's OWN machine history (median error 22%), so the
//     bar carries the RANGE it was built from rather than pretending to a number;
//   · a raw percentage is meaningless in September — a normal season is only ~10%
//     planted by the 15th and ramps to 91% by 15 October — so the NORMAL PACE
//     marker sits on the bar and the curve is drawn underneath it;
//   · farms the machine cannot measure are NAMED, never shown as 0%.
// ── THE INTENDED-ACRES OVERRIDE ─────────────────────────────────────────────
// Kevin, 2026-09-25: *"have the default denominator acres be their average but
// also allow us to override it if they tell us how many acres they are intending
// to plant."*
//
// The denominator is `expectedAcres()` — the mean of a farm's last machine
// seasons — and it stays the default. This records the other kind of fact: a
// number the grower actually gave. See api/_wheat-progress.js for the rule and
// sql/075_planting_intent.sql for why it is its own table.
//
// ⚠ BLANK CLEARS, AND THERE IS NO WAY TO TYPE A ZERO. "They are planting
// nothing" and "nobody has told us" are different facts; the first is a real
// statement that deserves a deliberate act somewhere it can be seen, and the
// second is what an empty box means. A stored 0 would read as a denominator of
// zero and score the farm as infinitely behind.
function IntentEditor({ farm, cropYear, onSaved }) {
  const stated = farm && farm.expected_state === "stated";
  const [acres, setAcres] = React.useState(stated && farm.expected_acres != null ? String(farm.expected_acres) : "");
  const [on, setOn] = React.useState(farm && farm.expected_stated_on ? String(farm.expected_stated_on).slice(0, 10) : "");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await authedFetch("/api/market?fn=jd-planting-intent", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          farmer_id: farm.farmer_id, crop: "WHEAT", crop_year: cropYear,
          // An empty box is a CLEAR, not a zero — the endpoint deletes the row
          // and the page goes back to the machine average.
          intended_acres: acres.trim() === "" ? null : Number(acres),
          stated_on: on || null,
        }),
      });
      const j = await r.json();
      if (j && j.error) { setMsg(String(j.error)); setBusy(false); return; }
      setBusy(false);
      if (onSaved) onSaved();
    } catch (e) { setMsg(String((e && e.message) || e)); setBusy(false); }
  };

  const dirty = (acres.trim() === "" ? null : Number(acres)) !== (stated ? Number(farm.expected_acres) : null)
    || on !== (farm && farm.expected_stated_on ? String(farm.expected_stated_on).slice(0, 10) : "");

  const inp = { fontSize: 11.5, padding: "3px 7px", border: "1px solid #cfdccf", borderRadius: 6,
                boxSizing: "border-box", fontFamily: "inherit" };
  return (
    <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, color: "#60735f" }}>Intends to plant</span>
      <input value={acres} onChange={(e) => setAcres(e.target.value.replace(/[^0-9.]/g, ""))}
        onFocus={(e) => e.target.select()} inputMode="decimal" placeholder="acres"
        style={{ ...inp, width: 78 }} />
      <span style={{ fontSize: 11, color: "#60735f" }}>ac · told us</span>
      <input type="date" value={on} onChange={(e) => setOn(e.target.value)} style={{ ...inp, width: 138 }} />
      <button onClick={save} disabled={busy || !dirty}
        style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 7, cursor: busy || !dirty ? "default" : "pointer",
                 border: "1px solid " + (dirty ? "#2d4a2d" : "#cfdccf"), background: dirty ? "#2d4a2d" : "#fff",
                 color: dirty ? "#fff" : "#677167" }}>
        {busy ? "Saving…" : acres.trim() === "" && stated ? "Clear" : "Save"}
      </button>
      {/* Says what a blank does, where the person about to blank it can read it. */}
      <span style={{ fontSize: 10.5, color: "#677167" }}>
        {stated ? "blank to go back to their machine average" : "leave blank to use their machine average"}
      </span>
      {msg && <span style={{ fontSize: 11, color: "#c0392b", fontWeight: 600 }}>{msg}</span>}
    </div>
  );
}

function WheatPlantingPanel({ isMobile = false, isPortal = false, restrictFarmerIds = null }) {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen] = useState(new Set());

  useEffect(() => {
    let alive = true;
    const ids = (restrictFarmerIds || []).filter(Boolean);
    const q = ids.length ? `&farmer=${ids.map(encodeURIComponent).join(",")}` : "";
    if (isPortal && !q) { setRep(null); return; }
    authedFetch(`/api/market?fn=jd-wheatplanting${q}`)
      .then(r => r.json())
      .then(d => { if (alive) { if (d && d.error) setErr(String(d.error)); else setRep((d && d.report) || null); } })
      .catch(e => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [reloadKey, restrictFarmerIds, isPortal]);

  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 12, padding: isMobile ? 14 : 18 };
  const pct1 = (v) => v == null ? "—" : `${(v * 100).toFixed(v < 0.1 ? 1 : 0)}%`;

  if (err) return <div style={{ ...card, color: "#c0392b", fontSize: 13 }}>Couldn't load wheat planting: {err}</div>;
  if (!rep) return <div style={{ ...card, color: "#60735f", fontSize: 13 }}><UiSpinner />Reading wheat planting across the book…</div>;

  const b = rep.book;
  const started = rep.farms.filter(f => f.planted_acres > 0);
  const measurable = rep.farms.filter(f => f.expected_acres != null);
  const unmeasurable = rep.excluded.farms_without_machine_history || [];
  const noCover = rep.excluded.farms_planting_without_coverage || [];
  const pending = rep.excluded.farms_coverage_pending || [];

  // ⚠ THE VERDICT IS A GAP IN ACRES, NOT A RATIO OF SHARES. Against a 4% normal in
  // early September, "half of normal" is a rounding error dressed as an alarm. What
  // an agent can act on is how many acres sit between here and a normal pace.
  const gapAc = b.expected_acres > 0 && b.normal_share_today != null
    ? Math.round(b.expected_acres * (b.normal_share_today - (b.share || 0))) : null;
  // One good day is several hundred acres across this book, so a gap inside that is
  // not news. Measured: the ramp moves ~20 points of the book in a week.
  const TOL_AC = Math.max(150, Math.round(b.expected_acres * 0.05));
  const verdict = gapAc == null ? null
    : gapAc > TOL_AC ? { t: "Behind a normal pace", c: "#a9791f", bg: "#fff8ec" }
    : gapAc < -TOL_AC ? { t: "Ahead of a normal pace", c: "#2d6a2d", bg: "#f0f7ec" }
    : { t: "On a normal pace", c: "#2d4a2d", bg: "#f2f6f2" };

  // ── the season ramp, drawn from the same curve the verdict uses ─────────────
  const curve = rep.curve_points || null;
  const W = 520, H = 92, PAD = 26;
  const xOf = (d) => PAD + ((d - 32) / (107 - 32)) * (W - PAD * 2);
  const yOf = (v) => H - 18 - v * (H - 34);
  const todayIdx = rep.today_index;

  const bar = (share, normal, h) => (
    <div style={{ position: "relative", height: h, background: "#e8eee8", borderRadius: h / 2, overflow: "visible" }}>
      <div style={{ width: `${Math.min(100, (share || 0) * 100)}%`, height: "100%", background: "#4a8a3a", borderRadius: h / 2 }} />
      {normal != null && (
        <div title={`A normal season is ${pct1(normal)} planted by today`}
          style={{ position: "absolute", left: `${Math.min(100, normal * 100)}%`, top: -3, bottom: -3, width: 2, background: "#1a2e1a" }} />
      )}
    </div>
  );

  return (
    <div>
      {/* ── HEADLINE ─────────────────────────────────────────────────────── */}
      <div style={{ ...card, background: "#12240f", border: "none", marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: "#8fbf6a" }}>
              Winter wheat · crop year {rep.crop_year}
            </div>
            <div style={{ fontSize: isMobile ? 27 : 34, fontWeight: 800, color: "#fff", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              {fmt(b.planted_acres, 0)}<span style={{ fontSize: 16, color: "#9ab88a", fontWeight: 600 }}> of ~{fmt(b.expected_acres, 0)} ac</span>
            </div>
            <div style={{ fontSize: 12, color: "#9ab88a", marginTop: 2 }}>
              {pct1(b.share)} planted · {b.farms_started} of {b.farms_measured} farms started
            </div>
            {/* ⚠ THE HEADLINE IS A FLOOR WHILE ATTRIBUTION IS OUTSTANDING, AND SAYS SO.
                A percentage that reads as the season's state while a tenth of the work
                is sitting un-attributed is the page making a claim about the farm out of
                a fact about our pipeline. Named here rather than folded in, because the
                pending acres are per-operation and the headline is CLU-deduped ground. */}
            {b.pending_attribution_acres > 0 && (
              <div title="These seedings have arrived from the machines; the field attribution that turns a planter track into CLU acres has not been built for them yet. They are measured per operation rather than per CLU, so they are not added to the figure above."
                style={{ fontSize: 11.5, color: "#e0c56a", marginTop: 4, fontWeight: 700, cursor: "help" }}>
                At least {fmt(b.pending_attribution_acres, 0)} ac more is reported and awaiting field
                attribution on {b.pending_attribution_farms} farm{b.pending_attribution_farms === 1 ? "" : "s"} — this figure is a floor.
              </div>
            )}
          </div>
          {verdict && (
            <div style={{ background: verdict.bg, color: verdict.c, borderRadius: 10, padding: "10px 14px", minWidth: 190 }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>{verdict.t}</div>
              <div style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.45 }}>
                A normal season is <b>{pct1(b.normal_share_today)}</b> in by today.
                {gapAc != null && Math.abs(gapAc) > TOL_AC
                  ? <> That is about <b>{fmt(Math.abs(gapAc), 0)} ac</b> {gapAc > 0 ? "behind" : "ahead"}.</>
                  : <> The book is within a day's work of it.</>}
              </div>
            </div>
          )}
        </div>
        <div style={{ marginTop: 14 }}>{bar(b.share, b.normal_share_today, 14)}</div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "#7a9a6a", marginTop: 5 }}>
          <span>0</span>
          <span style={{ color: "#cfe0c0" }}>▲ normal pace today</span>
          <span>~{fmt(b.expected_acres, 0)} ac</span>
        </div>
      </div>

      {/* ── THE SEASON RAMP ──────────────────────────────────────────────── */}
      {curve && (
        <div style={{ ...card, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#1a2e1a", marginBottom: 2 }}>When this book plants wheat</div>
          <div style={{ fontSize: 11.5, color: "#60735f", marginBottom: 8 }}>
            Median of {rep.method.curve_seasons} past seasons. The work is concentrated: about
            a third of the crop goes in during the last week of September, and nine in ten acres are
            in the ground by mid-October.
          </div>
          <div style={{ overflowX: "auto" }}>
            <svg width={W} height={H} style={{ display: "block", maxWidth: "100%" }}>
              <polyline fill="none" stroke="#c8dcc0" strokeWidth="2"
                points={curve.map(([d, v]) => `${xOf(d)},${yOf(v)}`).join(" ")} />
              {b.share != null && todayIdx != null && (
                <circle cx={xOf(todayIdx)} cy={yOf(b.share)} r="4.5" fill="#4a8a3a" />
              )}
              {todayIdx != null && (
                <>
                  <line x1={xOf(todayIdx)} y1="8" x2={xOf(todayIdx)} y2={H - 18} stroke="#1a2e1a" strokeWidth="1.5" strokeDasharray="3 3" />
                  <text x={xOf(todayIdx)} y="6" fontSize="9.5" fill="#1a2e1a" textAnchor="middle" fontWeight="700">today</text>
                </>
              )}
              {[[32, "Sep 1"], [53, "Sep 22"], [76, "Oct 15"], [107, "Nov 15"]].map(([d, l]) => (
                <text key={l} x={xOf(d)} y={H - 5} fontSize="9.5" fill="#60735f" textAnchor="middle">{l}</text>
              ))}
            </svg>
          </div>
        </div>
      )}

      {/* ── PER FARM ─────────────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", marginBottom: 10 }}>
          By farm · {measurable.length} measurable
        </div>
        {measurable.map((f) => {
          const isOpen = open.has(f.farmer_id);
          return (
            <div key={f.farmer_id} style={{ borderTop: "1px solid #f0f4f0", padding: "11px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
                onClick={() => setOpen((s) => { const n = new Set(s); n.has(f.farmer_id) ? n.delete(f.farmer_id) : n.add(f.farmer_id); return n; })}>
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  {/* ⚠ ON A PHONE THIS LINE WRAPS INSTEAD OF TRUNCATING, because the
                      badges live INSIDE it. `nowrap` + `textOverflow: ellipsis` is right
                      for the NAME — a farm name that runs on should trail off — but the
                      provenance badges after it ("vs insured acres", "one season only",
                      "not insured for wheat") then get clipped with it and there is no
                      way to reach them. Measured at 390px: WOODLAWN FARMS INC BY JOHN
                      MORGAN pushes its badge to 398px, 8px past the viewport, hidden.
                      That badge is the only thing on the row saying the bar is measured
                      against a different ruler, so losing it on mobile is losing the
                      caveat and keeping the number. Pre-existing — the farm-name column
                      shrinks (`flex: 1 1 auto`) and this has always clipped on a narrow
                      screen; it surfaced once the sideways drag beside it was fixed. */}
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a",
                    whiteSpace: isMobile ? "normal" : "nowrap", overflow: "hidden",
                    textOverflow: isMobile ? "clip" : "ellipsis" }}>
                    {f.name || "—"}
                    {f.expected_state === "thin" && (
                      <span title="Only one past season of machine wheat — this expectation is last year's acres, not an average."
                        style={{ fontSize: 10, color: "#a9791f", marginLeft: 6, fontWeight: 700, cursor: "help" }}>one season only</span>
                    )}
                    {/* ⚠ A DENOMINATOR FROM INSURANCE SAYS SO. It is a DIFFERENT
                        measurement from the numerator — machine acres against acres
                        reported to RMA — and this page's own footer already records
                        machine coverage running 58%–149% of insured. It is not a
                        worse predictor (median error 16.5% over 9 closed seasons,
                        against the machine rule's 22.2%), but a reader comparing two
                        farms has to know one bar is measured against a different
                        ruler. Silence here would be the Lismore trap on a progress
                        bar. */}
                    {f.expected_state === "insured" && (
                      <span title={`No usable machine wheat history for this farm, so the expectation is their own INSURED wheat acreage`
                          + (Array.isArray(f.expected_years) && f.expected_years.length
                            ? ` (CY${f.expected_years.join(", CY")})` : "")
                          + `. That is a different measurement from the machine acres in the bar: across the farms where both exist, machine acres have run 0.78x to 1.31x insured, median 0.97.`}
                        style={{ fontSize: 10, color: "#7a6a3a", marginLeft: 6, fontWeight: 700, cursor: "help" }}>
                        vs insured acres
                      </span>
                    )}
                    {/* A STATED FIGURE SAYS SO, AND SAYS WHAT IT REPLACED. "They
                        told us 900 and have averaged 1,130" is the interesting
                        case; a bare number that quietly differs from the machine
                        history is the one that gets mistrusted. */}
                    {f.expected_state === "stated" && (
                      <span title={`The grower told us they intend to plant ${fmt(f.expected_acres, 0)} ac`
                          + (f.expected_stated_on ? ` (stated ${String(f.expected_stated_on).slice(0, 10)})` : "")
                          + (f.expected_from_history != null ? `. Their machine average is ~${fmt(f.expected_from_history, 0)} ac.` : ".")
                          + (f.expected_note ? ` — ${f.expected_note}` : "")}
                        style={{ fontSize: 10, color: "#2d6a2d", marginLeft: 6, fontWeight: 700, cursor: "help" }}>
                        grower stated
                        {f.expected_from_history != null && Math.abs(f.expected_from_history - f.expected_acres) >= 1
                          ? ` · usually ~${fmt(f.expected_from_history, 0)}` : ""}
                      </span>
                    )}
                    {/* ⚠ COVERAGE IS AN ANNOTATION, NEVER AN EXCLUSION. Gating the
                        report on this year's coverage detail dropped LAWNEL, who
                        plants ~426 ac every season and whose CY2027 policy exists
                        with no rows yet. The two states mean different things and
                        the page says which. */}
                    {f.coverage_state === "pending" && (
                      <span title={`Insured wheat last year; the CY${rep.crop_year} coverage detail has not arrived yet. Fall sales closing is around 30 September.`}
                        style={{ fontSize: 10, color: "#7a8fa0", marginLeft: 6, fontWeight: 700, cursor: "help" }}>coverage not written yet</span>
                    )}
                    {f.coverage_state === "uninsured" && (
                      <span title="No wheat coverage this crop year or last — these acres go in uninsured."
                        style={{ fontSize: 10, color: "#a9791f", marginLeft: 6, fontWeight: 700, cursor: "help" }}>not insured for wheat</span>
                    )}
                  </div>
                  <div style={{ marginTop: 5 }}>{bar(f.share, b.normal_share_today, 8)}</div>
                </div>
                {/* ⚠ THIS COLUMN MUST BE ALLOWED TO SHRINK. At `flex: "0 0 auto"` it is
                    sized by its widest child and never gives way, so on a narrow phone the
                    row runs past the viewport and the whole page drags sideways. Measured
                    at 360px (a small Android; an SE is 375): EIGHT elements past the edge,
                    and they are the ORDINARY ones — "196% · 13 fields · started 09-12",
                    the county line, "1,010 ac of this not yet matched to an insured
                    field". That was true before the insurability line existed; 390px
                    happened to be wide enough to hide it.
                    `0 1 auto` + `minWidth: 0` keeps the column at its content width when
                    there is room — so nothing changes on a desktop — and lets it give way
                    and wrap when there is not. The name column beside it is already
                    `flex: "1 1 auto"`, so it takes the slack. */}
                <div style={{ textAlign: "right", flex: "0 1 auto", minWidth: 0 }}>
                  <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
                    {/* ⚠ THE TILDE IS THE PROVENANCE AND IT HAS TO GO WHEN THE
                        GROWER TOLD US (Kevin, 2026-09-25). `~1,034 ac` means "we
                        averaged their last seasons"; a stated figure is a
                        different kind of fact and must not wear the same mark.
                        Everything else about the row is unchanged. */}
                    {fmt(f.planted_acres, 0)} <span style={{ color: "#60735f", fontSize: 11.5, fontWeight: 600 }}>
                      / {f.expected_state === "stated" ? "" : "~"}{fmt(f.expected_acres, 0)} ac</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#60735f" }}>
                    {pct1(f.share)}
                    {f.fields > 0 ? ` · ${f.fields} field${f.fields === 1 ? "" : "s"}` : ""}
                    {f.first_seeding ? ` · started ${String(f.first_seeding).slice(5)}` : ""}
                  </div>
                  {/* ── ACRES BY COUNTY ───────────────────────────────────────
                      Kevin, 2026-10-09: "Wheat Planting needs to show acres planted
                      by county. Use same county splitting tool, as farmers may cross
                      county line." That tool is `jd_field_county` — the operation's
                      own grid binned per county — so a field straddling a line is
                      SPLIT rather than assigned to whichever county won. Measured
                      over the CY2027 window: CY FARMS has two such operations across
                      the Monroe/Genesee line.

                      The counties sum to the acreage printed above them, and where
                      an operation has no county split yet that remainder is NAMED.
                      Folding it into the largest county would state a county nobody
                      measured; dropping it would make the row fail to add up. */}
                  {Array.isArray(f.counties) && f.counties.length > 0 && (
                    <div style={{ fontSize: 10.5, color: "#5a7a5a", marginTop: 2 }}>
                      {f.counties.map(function (c, i) {
                        return (
                          <span key={c.county}>
                            {i > 0 ? " · " : ""}
                            <span style={{ fontWeight: 700 }}>{c.county}</span>{" "}
                            {fmt(c.acres, 0)} ac
                          </span>
                        );
                      })}
                      {/* ⚠ HALF AN ACRE, NOT ZERO. The guard was `> 0`, and a remainder
                          of 0.2 ac renders through fmt(…,0) as "0 ac county not yet
                          split" — a line that reads as a defect while saying nothing.
                          Measured on the live panel: HOWLETT, RAUBERDALE and GARY SWEDE
                          all printed it. Below half an acre the figure cannot show
                          itself, so it is not shown; those acres are still inside the
                          headline, and the counties below it are short by an amount
                          that rounds away at the same precision. */}
                      {f.county_unattributed_acres >= 0.5 ? (
                        <span title={`${f.county_ops_unsplit} seeding${f.county_ops_unsplit === 1 ? "" : "s"} have no county split yet — the job that bins the planter track onto counties is budgeted and lags the drill. These acres are in the figure above and are not assigned to any county.`}
                          style={{ color: "#8a6100", cursor: "help" }}>
                          {" · " + fmt(f.county_unattributed_acres, 0) + " ac county not yet split"}
                        </span>
                      ) : ""}
                      {f.county_ops_straddling > 0 ? (
                        <span title={`${f.county_ops_straddling} field${f.county_ops_straddling === 1 ? "" : "s"} cross a county line and are split between counties rather than assigned to one.`}
                          style={{ color: "#60735f", cursor: "help" }}>
                          {" · " + f.county_ops_straddling + " cross a line"}
                        </span>
                      ) : ""}
                    </div>
                  )}
                  {/* ── RMA'S OWN PLANTING WINDOW ──────────────────────────────────
                      Kevin, 2026-10-12: "Wheat planting should follow RMA insurability
                      rules." The engine behind this (api/_wheat-progress.js, reading the
                      `rma_dates` ADM load) computed every figure below and NOTHING
                      rendered it — `insurability` appeared zero times in this file while
                      the payload carried status, window, the acre split and a per-field
                      breakdown. Found by screenshotting the deployed page, not by any
                      checker: 121 assertions and 48 mutations all guarded a rule whose
                      output never reached a reader.

                      ⚠ THE DATES ARE RMA'S, NOT OURS, and `unknown` NAMES ITSELF rather
                      than reading as timely. A county RMA publishes no window for is a
                      county we cannot judge — saying nothing there would quietly imply
                      every acre is fine, which is the one reading that costs money. */}
                  {f.insurability && (function () {
                    var ins = f.insurability, w = ins.window || {};
                    var day = function (d) {
                      if (!d) return "";
                      var p = String(d).split("-");
                      return Number(p[2]) + " " + ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(p[1]) - 1];
                    };
                    // ⚠ THIS LINE MUST BE CAPPED OR IT DRAGS THE WHOLE PAGE SIDEWAYS.
                    // The column it sits in is `flex: "0 0 auto"` — it never shrinks, so it
                    // is sized by its WIDEST child, and every other child here is short
                    // (a percentage, three county names). At ~100 characters this one set
                    // the row width, pushed the farm names off screen and gave the page a
                    // horizontal scroll on a phone. Kevin, 2026-10-13: "The mobile UI is
                    // all messed up." I shipped it having rendered only at 1600px.
                    //
                    // Capped in `ch` rather than px so it tracks the font size instead of
                    // a magic number, and `marginLeft: auto` keeps it against the right
                    // edge once it is narrower than the column.
                    var wrapStyle = { maxWidth: isMobile ? "40ch" : "96ch", marginLeft: "auto", whiteSpace: "normal" };
                    if (!w.final_planting_date) {
                      // ⚠ THE ENGINE'S OWN REASON, VERBATIM. Paraphrasing it here would be a
                      // second copy of a rule that already states itself, and the reasons are
                      // not interchangeable — "no insured county on file" is a gap in OUR data,
                      // while a county RMA genuinely does not publish is a gap in RMA's.
                      return (
                        <div style={{ fontSize: 10.5, color: "#8a6100", marginTop: 2, ...wrapStyle }}>
                          {"RMA planting window not assessed" + (ins.reason ? " — " + ins.reason : " for this county")}
                        </div>
                      );
                    }
                    var late = Number(ins.late_acres) || 0;
                    var past = Number(ins.past_late_acres) || 0;
                    var unk = Number(ins.unknown_acres) || 0;
                    if (late <= 0 && past <= 0 && unk <= 0) {
                      return (
                        <div style={{ fontSize: 10.5, color: "#5a7a5a", marginTop: 2, ...wrapStyle }}>
                          {"All in by RMA's " + day(w.final_planting_date) + " final planting date"}
                        </div>
                      );
                    }
                    // The slide is RMA's 1%/day inside the late period, already computed per
                    // field by the engine. Several fields can sit at different day counts, so
                    // the range is printed rather than one of them.
                    var lf = Array.isArray(ins.late_fields) ? ins.late_fields : [];
                    var slides = lf.map(function (x) { return Number(x.guarantee_slide_pct) || 0; });
                    var lo = slides.length ? Math.min.apply(null, slides) : 0;
                    var hi = slides.length ? Math.max.apply(null, slides) : 0;
                    var slideTxt = !slides.length ? "" : (lo === hi ? lo + "%" : lo + "–" + hi + "%");
                    var daysTxt = !lf.length ? "" : (function () {
                      var d = lf.map(function (x) { return Number(x.days_late) || 0; });
                      var a = Math.min.apply(null, d), b2 = Math.max.apply(null, d);
                      return a === b2 ? a + (a === 1 ? " day" : " days") : a + "–" + b2 + " days";
                    })();
                    return (
                      <div style={{ fontSize: 10.5, color: "#8a6100", marginTop: 2, ...wrapStyle }}>
                        {late > 0 ? (
                          <span title={lf.map(function (x) {
                            return x.field + ": " + fmt(x.acres, 1) + " ac on " + x.seeded + " — " + x.days_late +
                              " day" + (x.days_late === 1 ? "" : "s") + " late, " + x.guarantee_slide_pct + "% guarantee slide";
                          }).join("\n")} style={{ cursor: lf.length ? "help" : "default" }}>
                            <b>{fmt(late, 1)} ac</b>{" after RMA's " + day(w.final_planting_date) + " final planting date"}
                            {daysTxt ? " — " + daysTxt + " late" : ""}
                            {slideTxt ? ", " + slideTxt + " guarantee slide" : ""}
                            {w.end_of_late_planting_date ? " · late period ends " + day(w.end_of_late_planting_date) : ""}
                          </span>
                        ) : null}
                        {past > 0 ? (
                          <span style={{ color: "#a33", display: "block" }}>
                            <b>{fmt(past, 1)} ac</b>{" after the " + day(w.end_of_late_planting_date) + " end of the late planting period"}
                          </span>
                        ) : null}
                        {unk > 0 ? (
                          <span style={{ color: "#60735f", display: "block" }}>
                            {fmt(unk, 1) + " ac with no seeding date on file — not judged"}
                          </span>
                        ) : null}
                      </div>
                    );
                  })()}
                  {/* ⚠ THE PACE FIGURE IS NOW THE MACHINE'S OWN, SO THIS LINE CHANGED MEANING.
                      Kevin, 2026-09-23: "Field progress needs to work only off JD data, or else
                      we won't know how pace is actually going." `planted_acres` is field-deduped
                      seeding acreage out of `jd_field_operations`; `jd_field_ftf` — which bins the
                      planter track onto insured CLUs — is built by a separate budgeted job and
                      lags the drilling window it matters in. T & D CORCORAN read "17 of ~233 ac ·
                      7.4%" on 2026-09-23 with 110.7 ac in the ground. He was 47% in.

                      So this is no longer "at least this much MORE": those acres are already in
                      the figure above. It is how much of what is shown has not yet been tied to
                      an insured CLU — a statement about our pipeline, which is what it always
                      was. Adding it now would count the same ground twice. */}
                  {f.pending_acres > 0 && (
                    <div title={`Of the ${fmt(f.planted_acres, 0)} ac shown, ${fmt(f.pending_acres, 0)} ac across ${f.pending_operations} seeding${f.pending_operations === 1 ? "" : "s"} have not yet been tied to an insured CLU${f.pending_oldest ? `, the oldest from ${String(f.pending_oldest).slice(5)}` : ""}. That is our attribution job catching up, not unplanted ground — the acres are already counted above.`}
                      style={{ fontSize: 10.5, color: "#8a6100", fontWeight: 700, marginTop: 2, cursor: "help" }}>
                      {fmt(f.pending_acres, 0)} ac of this not yet matched to an insured field
                    </div>
                  )}
                </div>
              </div>
              {isOpen && (
                <div style={{ marginTop: 9, paddingLeft: 2, fontSize: 11.5, color: "#5a7a5a", lineHeight: 1.6 }}>
                  {/* ⚠ THE EXPECTATION IS A RANGE, AND THE PAGE SAYS SO. A stated
                      figure has no range of its own — it is one number somebody
                      gave us — so the machine history is shown beneath it rather
                      than in place of it. */}
                  {f.expected_state === "stated" ? (
                    <div>
                      <b>{fmt(f.expected_acres, 0)} ac</b> intended — stated by the grower
                      {f.expected_stated_on ? ` on ${String(f.expected_stated_on).slice(0, 10)}` : ""}
                      {f.expected_stated_by ? `, recorded by ${f.expected_stated_by}` : ""}.
                      {f.expected_from_history != null && (
                        <> Their machines average <b>~{fmt(f.expected_from_history, 0)} ac</b>.</>
                      )}
                      {f.expected_note ? <div style={{ marginTop: 2, fontStyle: "italic" }}>{f.expected_note}</div> : null}
                    </div>
                  ) : (
                    <div>
                      {/* ⚠ A RANGE IS NOT A MEAN. This read "297–490 ac — the mean of CY2024
                          and CY2026": 297 and 490 are the two SEASONS and their mean is 393,
                          which is the `~393 ac` in the headline above and appeared nowhere in
                          the sentence explaining it. So the one number the row is measured
                          against was missing from its own explanation, and the two that were
                          there wore the wrong label. It states the mean and then its inputs,
                          each against its own year — looked up per year rather than printed
                          from expected_low/high, which are SORTED and would pair the wrong
                          acreage with the wrong season the moment the later year is smaller. */}
                      Usually plants <b>~{fmt(f.expected_acres, 0)} ac</b> — the mean of{" "}
                      {f.expected_years.map(function (y) {
                        var h = (f.history || []).find(function (x) { return Number(x.crop_year) === Number(y); });
                        return "CY" + y + (h ? " " + fmt(h.acres, 0) + " ac" : "");
                      }).join(" and ")}, from this farm's own machines.
                    </div>
                  )}
                  {/* ── THE OVERRIDE (Kevin, 2026-09-25) ─────────────────────
                      *"allow us to override it if they tell us how many acres they
                      are intending to plant."* Blank saves as a CLEAR and the
                      denominator returns to the machine average — there is no way
                      to type a zero, because "planting nothing" and "nobody has
                      said" are different facts and only one of them belongs in a
                      text box. */}
                  {!isPortal && (
                    <IntentEditor farm={f} cropYear={rep.crop_year}
                      onSaved={() => setReloadKey((k) => k + 1)} />
                  )}
                  {/* ⚠ PAST SEASONS ONLY — THE CURRENT ONE IS ALREADY THE HEADLINE, AND IT
                      DISAGREES WITH IT BY CONSTRUCTION. `history` comes from `seasonHistory`,
                      which deduplicates `jd_field_ftf` per CLU; the headline is machine
                      seeding acreage out of `jd_field_operations`. For a CLOSED season those
                      agree closely (SULLIVAN CY2024 296.6 vs 298.0, CY2026 489.9 vs 495.6)
                      because the CLU attribution has caught up. MID-SEASON it has not — that
                      job is budgeted and lags the drilling window, which is the whole reason
                      the headline was moved off it — so CY2027 read 471 ac directly beneath a
                      headline of 508. One card, two numbers for this year's wheat, with
                      nothing to say they were measured differently.
                      The lag is already named on the row ("32 ac of this not yet matched to
                      an insured field"), so dropping the current year here loses nothing and
                      removes the contradiction. It also makes this line agree with the
                      sentence above it, which has always excluded the current year from the
                      mean. */}
                  {f.history.filter(function (h) { return Number(h.crop_year) !== Number(rep.crop_year); }).length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      History: {f.history
                        .filter(function (h) { return Number(h.crop_year) !== Number(rep.crop_year); })
                        .slice(0, 6).map(h => `CY${h.crop_year} ${fmt(h.acres, 0)} ac`).join(" · ")}
                    </div>
                  )}
                  {!f.insures_wheat && (
                    <div style={{ marginTop: 4, color: "#a9791f" }}>No CY{rep.crop_year} wheat coverage on file yet.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!measurable.length && <div style={{ fontSize: 12.5, color: "#60735f" }}>No connected farm has enough machine wheat history to measure yet.</div>}
      </div>

      {/* ── WHAT THIS PAGE CANNOT SEE ────────────────────────────────────── */}
      {(unmeasurable.length > 0 || noCover.length > 0 || pending.length > 0) && (
        <div style={{ ...card, background: "#fdf8ec", border: "1px solid #e0cf9a", marginBottom: 14 }}>
          {/* Insures wheat every year; this year's detail has not arrived. These
              farms are IN the report above — this is a paperwork note, not an
              exclusion, and conflating it with the next block is what dropped
              LAWNEL off the page entirely. */}
          {pending.length > 0 && (
            <div style={{ marginBottom: (noCover.length || unmeasurable.length) ? 12 : 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#5a6b7a", marginBottom: 4 }}>
                CY{rep.crop_year} wheat coverage not written yet — {pending.length} farm{pending.length === 1 ? "" : "s"}
              </div>
              <div style={{ fontSize: 11.5, color: "#5a6b7a", lineHeight: 1.55 }}>
                {pending.map(f => `${f.name}${f.expected_acres != null ? ` (usually ~${Math.round(f.expected_acres)} ac)` : ""}`).join(" · ")}
                <br />They carried wheat last crop year and are counted above. Fall sales closing is around 30 September.
              </div>
            </div>
          )}
          {noCover.length > 0 && (
            <div style={{ marginBottom: unmeasurable.length ? 12 : 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#7a5c10", marginBottom: 4 }}>
                Plants wheat, not insured for it — {noCover.length} farm{noCover.length === 1 ? "" : "s"}
              </div>
              <div style={{ fontSize: 11.5, color: "#6b5a2a", lineHeight: 1.55 }}>
                {noCover.map(f => `${f.name} (usually ~${Math.round(f.expected_acres)} ac)`).join(" · ")}
                <br />No wheat coverage this crop year <b>or last</b> — these acres go in uninsured.
              </div>
            </div>
          )}
          {unmeasurable.length > 0 && (
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#7a5c10", marginBottom: 4 }}>
                Insured for wheat, no machine history to measure against — {unmeasurable.length} farm{unmeasurable.length === 1 ? "" : "s"}
              </div>
              <div style={{ fontSize: 11.5, color: "#6b5a2a", lineHeight: 1.55 }}>
                {unmeasurable.map(f => `${f.name} (${f.reason})`).join(" · ")}
                <br />These are <b>not</b> counted as 0% — nothing here can say how many acres they intend.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── METHOD ───────────────────────────────────────────────────────── */}
      <div style={{ ...card, fontSize: 11.5, color: "#60735f", lineHeight: 1.6 }}>
        <b style={{ color: "#5a7a5a" }}>How this is worked out.</b>{" "}
        Acres planted come from the planter's own coverage, binned onto FSA fields, so ground
        worked twice counts once. The target is the mean of the last {rep.method.denominator_seasons || 2} seasons
        that farm's machines reported — a farm's own record, not its insured acres, because fall acreage is
        not reported until 15 November. That estimate carries a median error of about{" "}
        {Math.round((rep.method.denominator_median_error || 0) * 100)}% across {rep.method.backtest_cases || 67} past
        farm-seasons, which is why every figure here is written as "about" and shown with its range.
        {rep.excluded.spring_window_rows > 0 && <> Spring-seeded wheat is excluded ({rep.excluded.spring_window_rows} records) — a different crop on a different calendar.</>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// THE RADAR MAP — full-bleed, observed through now then HRRR forward
// ─────────────────────────────────────────────────────────────────────────────
// The Field Mapper's radar is an OVERLAY: it exists to answer "is this cell going
// to hit that field", so it lives under the crop polygons and shares the screen
// with the farm. This one is the opposite — the map IS the content, so it gets the
// viewport, edge to edge, with one strip of chrome at the bottom.
//
// It deliberately reuses radarSnapBbox / drawRadarFrame / the ?fn=radar manifest
// rather than growing its own copy. Those three are exactly where a second
// implementation would drift: the observed source is a WMS that has to be asked in
// the projection the forecast frames were rendered in, and getting it subtly wrong
// still draws something plausible. One code path, two pages.
//
// The basemap defaults to DARK GREY, not satellite. Reflectivity is a saturated
// green-through-magenta ramp, and over green summer imagery the low end vanishes
// into the fields — which is half of why the overlay version reads as "busy". On a
// desaturated base every dBZ step separates, which is the whole job here. Imagery
// stays one tap away for placing a cell against real ground.
function WeatherRadarMap({ isMobile = false, locs = [], onPickPoint = null }) {
  const [L, setL] = useState(typeof window !== "undefined" ? window.L : null);
  const elRef = useRef(null), mapRef = useRef(null);
  const radarRef = useRef(null);           // { wms, img } — owned by drawRadarFrame
  const pinsRef = useRef(null);
  const [man, setMan] = useState(null);
  const [idx, setIdx] = useState(0);
  const [play, setPlay] = useState(false);
  const [bbox, setBbox] = useState(null);
  const [opacity, setOpacity] = useState(0.8);
  const [ready, setReady] = useState(0);
  const [base, setBase] = useState("dark");   // dark | imagery
  const [tick, setTick] = useState(0);
  const [tap, setTap] = useState(null);       // { lat, lon }
  const [vh, setVh] = useState(typeof window !== "undefined" ? window.innerHeight : 900);

  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // The map takes the viewport minus the app chrome. A radar you have to scroll to
  // see the bottom of is a radar you cannot read the timeline on.
  const mapH = isMobile
    ? Math.max(360, Math.min(620, Math.round(vh - 300)))
    : Math.max(460, Math.min(900, Math.round(vh - 300)));

  useEffect(() => { if (!L) loadLeaflet().then(setL).catch(() => {}); }, [L]);

  // ── the map ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!L || !elRef.current || mapRef.current) return;
    // Open on the saved locations when there are any — this page is a watchlist
    // first, so the default view should already contain what is being watched.
    const pts = locs.filter((l) => Number.isFinite(Number(l.lat)) && Number.isFinite(Number(l.lon)));
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: true })
      .setView([42.9, -77.85], 8);
    if (pts.length === 1) map.setView([Number(pts[0].lat), Number(pts[0].lon)], 9);
    else if (pts.length > 1) {
      try { map.fitBounds(pts.map((l) => [Number(l.lat), Number(l.lon)]), { padding: [50, 50], maxZoom: 10 }); } catch (e) {}
    }
    map.on("click", (ev) => setTap({ lat: ev.latlng.lat, lon: ev.latlng.lng }));
    mapRef.current = map;
    setBbox(radarSnapBbox(map));
    setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 60);
    return () => { try { map.remove(); } catch (e) {} mapRef.current = null; radarRef.current = null; };
  // Built once when Leaflet lands. `locs` is read for the opening view only —
  // adding it here would tear the map down and rebuild it every time the watchlist
  // changed, losing wherever the user had panned to.
  }, [L]);   // eslint-disable-line

  // Basemap, swapped in place. Two reference layers over the dark base so towns and
  // roads still read — a radar echo with no place names under it is unplaceable.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    const layers = [];
    const add = (url, opts) => { const t = L.tileLayer(url, opts).addTo(map); layers.push(t); };
    if (base === "imagery") {
      add("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 18, attribution: "Imagery © Esri" });
    } else {
      add("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 16, attribution: "© Esri" });
    }
    add("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 18, opacity: 0.9 });
    return () => { for (const t of layers) { try { map.removeLayer(t); } catch (e) {} } };
  }, [L, base]);

  // ── manifest: the ONLY source of frame times ─────────────────────────────
  useEffect(() => {
    if (!L) return;
    let alive = true;
    fetch("/api/market?fn=radar&op=manifest")
      .then((r) => r.json())
      .then((j) => {
        if (!alive || !j || !j.ok || !Array.isArray(j.frames) || !j.frames.length) return;
        setMan(j);
        // Open on "now". Scrub left for what happened, right for what is coming.
        setIdx((prev) => {
          const nowIdx = j.frames.findIndex((f) => f.isNow);
          return prev === 0 ? (nowIdx >= 0 ? nowIdx : 0) : Math.min(prev, j.frames.length - 1);
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [L, tick]);

  // Keep it live. Left open through lunch, a radar that never re-reads its manifest
  // presents an hour-old frame as the present and still looks right.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Re-snap the bbox when the view settles somewhere new (forecast frames are
  // rendered per bbox; observed tiles don't care).
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    let timer = null;
    const settle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!mapRef.current) return;
        const b = radarSnapBbox(mapRef.current);
        setBbox((prev) => (prev && prev.join(",") === b.join(",") ? prev : b));
      }, 500);
    };
    map.on("moveend", settle);
    return () => { if (timer) clearTimeout(timer); try { map.off("moveend", settle); } catch (e) {} };
  }, [L, man]);

  // Preload the forecast frames so playback doesn't blank between them. Observed
  // frames are WMS tiles and hold their previous tiles while the next load.
  const fcCount = useMemo(() => (man ? man.frames.filter((f) => f.kind === "forecast").length : 0), [man]);
  useEffect(() => {
    if (!man || !bbox) return;
    let alive = true;
    setReady(0);
    let done = 0;
    const nowIdx = Math.max(0, man.frames.findIndex((f) => f.isNow));
    man.frames
      .map((fr, i) => ({ fr, i }))
      .filter(({ fr }) => fr.kind === "forecast")
      .sort((a, b) => Math.abs(a.i - nowIdx) - Math.abs(b.i - nowIdx))
      .forEach(({ fr }) => {
        const url = radarFrameUrl(fr, bbox, 1000, 620, "single");
        if (!url) return;
        const img = new Image();
        const t = () => { if (alive) setReady(++done); };
        img.onload = t; img.onerror = t;
        img.src = url;
      });
    return () => { alive = false; };
  }, [man, bbox]);

  // Draw the current frame.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L || !man) return;
    const fr = man.frames[Math.min(idx, man.frames.length - 1)];
    if (fr && fr.kind === "forecast" && !bbox) return;
    drawRadarFrame(L, map, radarRef, fr, bbox, opacity, "single");
  }, [L, man, idx, bbox, opacity]);

  // Saved locations, drawn on top of the weather. Labelled, because a ring with no
  // name is useless on a five-farm watchlist.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (pinsRef.current) { try { map.removeLayer(pinsRef.current); } catch (e) {} }
    const g = L.layerGroup().addTo(map);
    for (const l of locs) {
      const lat = Number(l.lat), lon = Number(l.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      L.circleMarker([lat, lon], { radius: 6, color: "#fff", weight: 2.5, fillColor: "#1a2e1a", fillOpacity: 1 })
        .addTo(g).bindTooltip(l.name || "", { permanent: !isMobile, direction: "right", className: "silas-wx-pin" });
    }
    pinsRef.current = g;
    return () => { try { map.removeLayer(g); } catch (e) {} };
  }, [L, locs, isMobile]);

  // The tapped point, so "what about here" has an answer that isn't a colour.
  const tapRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (tapRef.current) { try { map.removeLayer(tapRef.current); } catch (e) {} tapRef.current = null; }
    if (!tap) return;
    tapRef.current = L.circleMarker([tap.lat, tap.lon],
      { radius: 8, color: "#ffd34d", weight: 3, fillColor: "#1a2e1a", fillOpacity: 0.6 }).addTo(map);
  }, [L, tap]);

  // Playback. Holds a beat on the last frame so the loop reads as an ending rather
  // than a hard snap back to the start.
  useEffect(() => {
    if (!play || !man || !man.frames.length) return;
    const last = man.frames.length - 1;
    const t = setTimeout(() => setIdx((i) => (i >= last ? 0 : i + 1)), idx === last ? 900 : 320);
    return () => clearTimeout(t);
  }, [play, idx, man]);

  useEffect(() => {
    if (document.getElementById("silas-wx-pin-style")) return;
    const st = document.createElement("style");
    st.id = "silas-wx-pin-style";
    st.textContent = ".silas-wx-pin{background:rgba(26,46,26,.82);border:none;color:#fff;font-weight:800;"
      + "font-size:11px;box-shadow:none;padding:1px 6px;border-radius:5px}.silas-wx-pin::before{display:none}";
    document.head.appendChild(st);
  }, []);

  const frames = man ? man.frames : [];
  const fr = frames.length ? frames[Math.min(idx, frames.length - 1)] : null;
  const nowIdx = Math.max(0, frames.findIndex((f) => f.isNow));
  const isFc = fr && fr.kind === "forecast";
  const cached = ready >= fcCount;
  const clock = fr ? new Date(fr.t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) : "";
  const day = fr ? new Date(fr.t).toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" }) : "";
  const todayET = new Date().toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" });
  const deltaMin = fr && man ? Math.round((new Date(fr.t) - new Date(man.nowFrame)) / 60000) : 0;
  const rel = deltaMin === 0 ? "now"
    : `${deltaMin > 0 ? "+" : "−"}${Math.floor(Math.abs(deltaMin) / 60) ? `${Math.floor(Math.abs(deltaMin) / 60)}h ` : ""}${Math.abs(deltaMin) % 60 ? `${Math.abs(deltaMin) % 60}m` : ""}`.trim();
  const staleMin = man ? Math.round((Date.now() - new Date(man.nowFrame)) / 60000) : 0;

  const chip = (on) => ({
    cursor: "pointer", fontFamily: "inherit", borderRadius: 7, padding: "5px 10px",
    fontSize: 11.5, fontWeight: 800, border: `1px solid ${on ? "#1f3320" : "#d9e4d9"}`,
    background: on ? "#1f3320" : "rgba(255,255,255,.92)", color: on ? "#fff" : "#3a4a3a",
  });

  return (
    // Full-bleed on a phone: the page's own 12px padding is cancelled so the map
    // runs to both edges, which is most of what makes this feel like a radar rather
    // than a widget on a page.
    <div style={{ margin: isMobile ? "0 -12px" : 0 }}>
      <div style={{ position: "relative", height: mapH, overflow: "hidden",
                    borderRadius: isMobile ? 0 : 12, border: isMobile ? "none" : `1px solid ${T.line}`,
                    borderTop: isMobile ? `1px solid ${T.line}` : undefined, background: "#2b2f2b" }}>
        <div ref={elRef} style={{ position: "absolute", inset: 0 }} />

        {!L && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#a8d878", fontSize: 13, fontWeight: 700 }}>Loading map…</div>
        )}

        {/* Basemap toggle + opacity, top right, clear of Leaflet's zoom control. */}
        <div style={{ position: "absolute", top: 10, right: 10, zIndex: 800, display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          <div style={{ display: "inline-flex", gap: 4 }}>
            <button onClick={() => setBase("dark")} style={chip(base === "dark")}>Map</button>
            <button onClick={() => setBase("imagery")} style={chip(base === "imagery")}>Satellite</button>
          </div>
          <div title="Radar opacity"
               style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 9px", borderRadius: 7,
                        background: "rgba(255,255,255,.92)", border: "1px solid #d9e4d9" }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: "#60735f", letterSpacing: ".05em" }}>FADE</span>
            <input type="range" min="25" max="100" step="5" value={Math.round(opacity * 100)}
              onChange={(e) => setOpacity(Number(e.target.value) / 100)}
              style={{ width: 74, cursor: "pointer" }} />
          </div>
        </div>

        {/* Tapped-point readout. Coordinates plus a one-tap save, so a spot you are
            watching becomes a watchlist entry without typing latitude by hand. */}
        {/* top:82, not top:10 — Leaflet's zoom control owns the top-left corner and
            sits at z-index 1000, so a panel at 10 renders UNDERNEATH the +/- buttons. */}
        {tap && (
          <div style={{ position: "absolute", left: 10, top: 82, zIndex: 800, padding: "8px 11px", borderRadius: 8,
                        background: "rgba(26,46,26,.93)", color: "#e8f2e0", boxShadow: "0 4px 18px rgba(0,0,0,.42)" }}>
            <div style={{ ...uiNum, fontSize: 12, fontWeight: 800 }}>
              {tap.lat.toFixed(4)}, {tap.lon.toFixed(4)}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              {onPickPoint && (
                <button onClick={() => onPickPoint(tap)}
                  style={{ cursor: "pointer", fontFamily: "inherit", border: "none", borderRadius: 6, padding: "4px 9px",
                           fontSize: 11, fontWeight: 800, background: "#a8d878", color: "#1a2e1a" }}>
                  + Save this spot
                </button>
              )}
              <button onClick={() => setTap(null)}
                style={{ cursor: "pointer", fontFamily: "inherit", border: "1px solid #46603f", borderRadius: 6,
                         padding: "4px 9px", fontSize: 11, fontWeight: 700, background: "transparent", color: "#cfe6bf" }}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* ── the strip ──────────────────────────────────────────────────────
            Everything that describes the frame in one place along the bottom:
            play, where in time this is, whether it is measured or modelled, and
            the scale. Numbers only — the read is the user's. */}
        <div style={{ position: "absolute", left: 8, right: 8, bottom: 8, zIndex: 810,
                      padding: isMobile ? "7px 9px" : "8px 12px", borderRadius: 10,
                      background: "rgba(255,255,255,.94)", border: "1px solid #d9e4d9",
                      boxShadow: "0 6px 20px -10px rgba(0,0,0,.45)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 7 : 10, flexWrap: "wrap" }}>
            <button onClick={() => setPlay((p) => !p)} disabled={!cached || !frames.length}
              title={cached ? (play ? "Pause" : "Play the loop") : "Caching frames…"}
              style={{ cursor: cached ? "pointer" : "default", fontFamily: "inherit", border: "1px solid #cdd9cd",
                       borderRadius: 7, width: 32, height: 28, fontSize: 12, fontWeight: 800,
                       background: cached ? "#2d4a2d" : "#eef3ee", color: cached ? "#fff" : "#677167" }}>
              {play ? "❚❚" : "▶"}
            </button>
            <button onClick={() => { setPlay(false); setIdx(nowIdx); }} title="Jump back to the present"
              style={{ cursor: "pointer", fontFamily: "inherit", borderRadius: 7, padding: "5px 10px",
                       fontSize: 11, fontWeight: 800,
                       border: `1px solid ${idx === nowIdx ? "#2d4a2d" : "#cdd9cd"}`,
                       background: idx === nowIdx ? "#2d4a2d" : "#fff", color: idx === nowIdx ? "#fff" : "#3a4a3a" }}>
              NOW
            </button>
            <span style={{ ...uiNum, fontSize: 15, fontWeight: 800, color: "#1f3320", minWidth: 70 }}>
              {day && day !== todayET && <span style={{ fontSize: 11, color: "#60735f", marginRight: 4 }}>{day}</span>}
              {clock || "—"}
            </span>
            <span style={{ ...uiNum, fontSize: 11, fontWeight: 700, color: "#60735f", minWidth: 40 }}>{rel}</span>
            {/* Measured vs modelled, never blurred. */}
            {fr && (
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".05em", padding: "2px 7px", borderRadius: 6,
                             background: isFc ? "#fbf2e0" : "#e3f3e0", color: isFc ? "#7a5a20" : "#2d6a2d" }}>
                {isFc ? "HRRR FORECAST" : "OBSERVED"}
              </span>
            )}
            {!cached && fcCount > 0 && (
              <span style={{ fontSize: 10, color: "#677167" }}>caching {ready}/{fcCount}</span>
            )}
            {staleMin >= 25 && (
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "2px 7px", borderRadius: 6, background: "#fdeceb", color: "#a33" }}
                title="The newest frame is older than it should be — the refresh may have failed.">
                STALE {staleMin}m
              </span>
            )}
            {!isMobile && (
              <span style={{ marginLeft: "auto", fontSize: 10, color: "#677167" }}>
                {man && man.run ? `HRRR ${man.run.slice(8)}Z run` : ""}
              </span>
            )}
          </div>

          {/* The track. Ticks ARE the frames, so the observed → forecast seam and
              the 15-minute/hourly cadence change are visible in the geometry
              instead of needing a caption. Touch gets a range input over the
              painted track, because 37 tick buttons are 8px targets on a phone. */}
          {frames.length > 0 && (isMobile ? (
            <div style={{ marginTop: 7 }}>
              <div style={{ display: "flex", alignItems: "stretch", gap: 1, height: 12 }}>
                {frames.map((f, i) => (
                  <div key={f.t} style={{ flex: f.kind === "forecast" && f.stepMin === 60 ? 4 : 1, minWidth: 0, borderRadius: 2,
                                          borderLeft: f.isNow ? "2px solid #1f3320" : "none",
                                          background: i === idx ? "#1f3320" : f.kind === "forecast" ? "#e6c98a" : "#8fbf7f" }} />
                ))}
              </div>
              <input type="range" min="0" max={frames.length - 1} step="1" value={Math.min(idx, frames.length - 1)}
                onChange={(e) => { setPlay(false); setIdx(Number(e.target.value)); }}
                style={{ width: "100%", marginTop: 2, cursor: "pointer" }} />
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "stretch", gap: 1, marginTop: 7, height: 22 }}>
              {frames.map((f, i) => (
                <button key={f.t} onClick={() => { setPlay(false); setIdx(i); }}
                  title={`${new Date(f.t).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} · ${f.kind === "forecast" ? "HRRR forecast" : "observed"}`}
                  style={{ flex: f.kind === "forecast" && f.stepMin === 60 ? 4 : 1, minWidth: 0, cursor: "pointer", padding: 0,
                           border: "none", borderRadius: 2, borderLeft: f.isNow ? "2px solid #1f3320" : "none",
                           background: i === idx ? "#1f3320" : f.kind === "forecast" ? "#e6c98a" : "#8fbf7f",
                           opacity: i === idx ? 1 : 0.85 }} />
              ))}
            </div>
          ))}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4,
                        fontSize: 10, color: "#677167", gap: 8, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              dBZ
              {[["20", "#02fd02"], ["35", "#fdf802"], ["45", "#fd9500"], ["55", "#d40000"], ["65", "#f800fd"]].map(([lab, c]) => (
                <span key={lab} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: c, display: "inline-block" }} />{lab}
                </span>
              ))}
            </span>
            <span>{locs.length ? "tap the map for a point" : ""}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WEATHER — the radar map, what actually fell, and what is coming
// ─────────────────────────────────────────────────────────────────────────────
// Two columns of truth, deliberately side by side:
//   RECEIVED  MRMS gauge-corrected radar QPE — measured, ~1 km
//   FORECAST  NBM calibrated probability of measurable rain + expected amount
//
// A forecast with no gauge beside it can never be checked, and "0.20 expected /
// 0.61 already down" is both the most useful line here and the only way anyone
// learns whether to believe next week's number.
//
// Both endpoints take every saved point in ONE call: the GRIB records are
// identical across locations and only the sampled cell differs, so a five-farm
// watchlist costs what one location costs.
// The Weather page is open to EVERY portal user — it reads no book data. A saved
// location is private to whoever saved it (weather_locations RLS is
// `created_by = auth.uid()`, one rule for agents and farmers alike), and the
// forecast itself comes from `?fn=precip`, which takes coordinates and returns
// rain. Nothing on this screen touches the book EXCEPT the agent-only "Pick a
// farm" shortcut, which the portal does not render and does not fetch.
// ── WEATHER RECORDS — the evidence twin of the Weather page ────────────────────
// Kevin, 2026-10-13: a weather tool for "any insurance situation that could be weather
// related: Prevent Plant supporting documents … Freeze data … Drought … including FSA
// SDRP like programs where we need supporting documents for payments", with "customized
// sort and filter" and a location picked "on a map instead of tying it to a stored field".
//
// The Weather page (`rainwatch`) is FORWARD-looking and operational — radar, forecast,
// nowcast, fieldwork windows, alerts. This is the backward-looking half: what the
// weather DID, at a point, over an arbitrary window, in a form you can print and hand to
// an adjuster or to FSA. Linked rather than merged, because an operational forecast and
// an evidentiary record are read for different reasons and age differently.
//
// ⚠ EVERY NUMBER CARRIES WHERE IT CAME FROM. The arithmetic is in src/weather/wxEvidence.js
// and the queries in api/_wx-evidence.js; this renders them and adds no arithmetic of its
// own, so the screen and a printed sheet cannot disagree. The three facts that make this
// evidence rather than decoration — WHICH sample, HOW FAR away, WHICH instrument — are on
// screen at all times and are not collapsible.
const WX_PRESETS = [
  { key: "spring", label: "This spring", win: () => { const y = new Date().getUTCFullYear(); return [y + "-04-01", y + "-06-10"]; } },
  { key: "lastspring", label: "Last spring", win: () => { const y = new Date().getUTCFullYear() - 1; return [y + "-04-01", y + "-06-10"]; } },
  { key: "season", label: "This season", win: () => { const y = new Date().getUTCFullYear(); return [y + "-04-01", y + "-10-31"]; } },
  { key: "d30", label: "Last 30 days", win: () => { const t = new Date(); const a = new Date(t.getTime() - 29 * 86400000);
      return [a.toISOString().slice(0, 10), t.toISOString().slice(0, 10)]; } },
  { key: "d90", label: "Last 90 days", win: () => { const t = new Date(); const a = new Date(t.getTime() - 89 * 86400000);
      return [a.toISOString().slice(0, 10), t.toISOString().slice(0, 10)]; } },
];

// The printed sheet. ⚠ IT PRINTS THE ROWS ON SCREEN, filter and sort included — a
// document that silently showed a different set from the page it was made on would be
// the worst kind of wrong, because both look right on their own.
function WX_printRecord(data, view) {
  const rows = WX.applyView(data.series, view);
  const S = data.summary, snap = data.snap;
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const w = window.open("", "_blank");
  if (!w) return;
  writePrintWindow(w,
    "<html><head><title>Weather record</title><style>"
    + "body{font:12px/1.45 -apple-system,Segoe UI,Roboto,sans-serif;color:#1a2e1a;margin:26px}"
    + "h1{font-size:17px;margin:0 0 2px}h2{font-size:12px;margin:16px 0 5px;text-transform:uppercase;letter-spacing:.05em;color:#5c6f5c}"
    + "table{border-collapse:collapse;width:100%;font-size:10.5px}th,td{padding:3px 6px;border-bottom:1px solid #e4ece4;text-align:right}"
    + "th:first-child,td:first-child,th:last-child,td:last-child{text-align:left}"
    + ".p{background:#f4f8f4;border:1px solid #d9e4d9;border-radius:6px;padding:8px 11px;font-size:11px;margin:8px 0}"
    + ".w{background:#fdf6e8;border-color:#e8d9ae}"
    + "@media print{button{display:none}}</style></head><body>"
    + "<h1>Weather record</h1>"
    + "<div style='font-size:11px;color:#5c6f5c'>" + esc(data.window.from) + " to " + esc(data.window.to)
    + " &middot; " + esc(data.window.days) + " days</div>"
    + "<div class='p" + (snap.far ? " w" : "") + "'><b>Where this was measured.</b> Grid cell " + esc(snap.cell_id)
    + ", " + esc(snap.county) + " County, " + esc(snap.distance_mi) + " mi from the location requested ("
    + esc(data.point_label || "") + "). " + esc(snap.note) + "</div>"
    + "<div class='p'><b>How it was measured.</b> "
    + Object.entries(S.sources).map(([k, n]) => esc((WX.SOURCE_LABEL[k] || k) + " on " + n + " day" + (n === 1 ? "" : "s"))).join("; ")
    + ". Normals are gridMET 1995&ndash;2025, a " + esc(S.normal_years_min) + "-year or longer sample. Percent of normal"
    + " compares gridMET's own reading with that normal on the " + esc(S.normal_covers_days)
    + " of the " + esc(S.days_observed) + " observed days gridMET has published, so the two sides are one instrument."
    + (S.days_forecast ? " " + esc(S.days_forecast) + " forecast day(s) are shown but excluded from every total." : "")
    + "</div>"
    + "<h2>Summary</h2><table><tbody>"
    + "<tr><td>Rainfall</td><td>" + esc(S.precip_in) + " in measured</td><td>gridMET " + esc(S.precip_gm_in)
    + " in vs normal " + esc(S.normal_precip_in)
    + " in</td><td>" + esc(S.precip_pct_normal) + "% of normal</td></tr>"
    + "<tr><td>Wet days (&ge;0.10 in)</td><td>" + esc(S.wet_days) + "</td><td>heaviest " + esc(S.max_day_in)
    + " in</td><td>" + (S.longest_dry_run ? "longest dry run " + esc(S.longest_dry_run.days) + " d" : "") + "</td></tr>"
    + "<tr><td>Coldest</td><td>" + esc(S.coldest_f) + " F</td><td>" + esc(S.freeze_days) + " day(s) &le; "
    + esc(S.freeze_threshold_f) + " F</td><td>" + esc(S.freeze_events.length) + " freeze event(s)</td></tr>"
    + "<tr><td>Growing degree days</td><td>" + esc(S.gdd) + "</td><td>hottest " + esc(S.hottest_f)
    + " F</td><td>" + esc(S.days_observed) + " observed days</td></tr>"
    + "</tbody></table>"
    // ⚠ THE SHEET CARRIES THE SAME above/below/normal THE SCREEN DOES, from the same
    // rule. A document filed with FSA and the screen it was taken from must not be able
    // to disagree about which way a month ran — and a metric the stored normal cannot
    // answer for prints its VALUE and the reason, never a blank and never a comparison
    // against a normal computed at a different setting.
    + ((data.normals && data.normals.length)
        ? "<h2>Against normal for these dates</h2><table><thead><tr><th>Measure</th><th>This window</th>"
          + "<th>Normal</th><th>Departure</th><th>Reading</th></tr></thead><tbody>"
          + data.normals.map((m) => "<tr><td>" + esc(m.label) + "</td><td>"
              + esc(m.value == null ? "—" : m.value + " " + m.unit) + "</td><td>"
              + esc(m.normal == null ? "—" : m.normal + " " + m.unit) + "</td><td>"
              + esc(m.departure == null ? "—" : (m.departure > 0 ? "+" : "") + m.departure) + "</td><td>"
              + (m.comparable && m.state
                  ? esc(m.state === "near" ? "near normal" : m.state + " normal")
                    + (m.pct_of_normal != null ? esc(" · " + m.pct_of_normal + "% of normal") : "")
                    + esc(" · " + m.days_compared + " days compared")
                  : "<i>not compared &mdash; " + esc(m.reason || "") + "</i>")
              + "</td></tr>").join("")
          + "</tbody></table>"
          + "<p style='font-size:10px;color:#60735f'>Near normal means within "
          + esc(Math.round(WX.NEAR_BAND_RATIO * 100)) + "% (" + esc(WX.NEAR_BAND_F)
          + " F on temperatures). That band is a reading aid, not a significance test: the stored"
          + " normals carry a mean and a year count, and no spread from which to judge one.</p>"
        : "")
    + (S.freeze_events.length
        ? "<h2>Freeze events</h2><table><thead><tr><th>From</th><th>To</th><th>Days</th><th>Low F</th></tr></thead><tbody>"
          + S.freeze_events.map((e) => "<tr><td>" + esc(e.from) + "</td><td>" + esc(e.to) + "</td><td>"
            + esc(e.days) + "</td><td>" + esc(e.min_f) + "</td></tr>").join("") + "</tbody></table>"
        : "")
    + "<h2>Daily record" + (view.filter && view.filter !== "all" ? " &mdash; " + esc(WX.FILTERS[view.filter].label) : "") + "</h2>"
    // ⚠ "Normal to date", CUMULATIVE — it sits beside the cumulative actual and is read
    // against it. Kevin, 2026-10-13: "normal here is also confusing because its not
    // cumulative, what is normal rainfall for selected range?" The daily normal is
    // 0.06–0.20" next to an actual that has reached several inches, so the comparison
    // the column exists for could not be made.
    + "<table><thead><tr><th>Date</th><th>Rain in</th><th>Cumulative</th><th>gridMET to date</th><th>Normal to date</th><th>Max F</th><th>Min F</th>"
    + "<th>GDD</th><th>Source</th></tr></thead><tbody>"
    + rows.map((d) => "<tr><td>" + esc(d.date) + (d.is_forecast ? " (forecast)" : "") + "</td><td>"
        + esc(d.precip_in) + "</td><td>" + esc(d.precip_cum_in) + "</td><td>" + esc(d.gm_cum_in) + "</td><td>" + esc(d.normal_cum_in) + "</td><td>"
        + esc(d.tmax_f) + "</td><td>" + esc(d.tmin_f) + "</td><td>" + esc(d.gdd) + "</td><td>"
        + esc(d.precip_source || "") + "</td></tr>").join("")
    + "</tbody></table>"
    + "<p style='font-size:10px;color:#60735f;margin-top:14px'>" + esc(data.coverage.states)
    + " " + esc(data.coverage.grid.note) + " " + esc(data.coverage.field_precip.note)
    + " Wind, humidity and snow are not stored for past days.</p>"
    + "<p style='font-size:10px;color:#60735f'>Generated " + esc(data.generated_at) + " by SILAS, Clover Ag Risk Management.</p>"
    + "<button onclick='window.print()'>Print</button></body></html>");
}

// ── EXPORT ────────────────────────────────────────────────────────────────────
// Kevin, 2026-10-13: "Can we also be able to print/ export?"
//
// ⚠ THE PROVENANCE RIDES ON THE FILE, NOT BESIDE IT. A bare grid of dates and inches
// is the one artifact here that can be forwarded, pasted into a claim file and read a
// year later by somebody who never saw the page — and at that point WHERE it was
// measured, HOW FAR that is from the ground in question, and WHICH instrument said so
// are unrecoverable. This module's whole contract is that no figure travels without
// them, and a CSV is the easiest place to lose them. They are comment lines at the top,
// which Excel shows and a parser can skip.
// ⚠ AND EVERY DAY CARRIES ITS OWN SOURCE COLUMN, because the window is routinely mixed
// — MRMS on recent days and gridMET before them — so one header line cannot speak for
// the file.
function WX_exportCsv(data, view) {
  const rows = WX.applyView(data.series, view);
  const S = data.summary, snap = data.snap;
  // RFC4180: quote everything, double the quotes inside. A field name or a reason can
  // carry a comma, and a CSV that splits a sentence into two columns is silent.
  const q = (v) => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
  const L = [];
  L.push("# SILAS Weather Record — Clover Ag Risk Management");
  L.push("# window," + data.window.from + " to " + data.window.to + "," + data.window.days + " days");
  L.push("# location," + (data.point_label || "") + ",lat " + snap.lat + ",lon " + snap.lon);
  L.push("# measured at,grid cell " + snap.cell_id + "," + snap.county + " County,"
    + snap.distance_mi + " mi from the location requested");
  if (snap.note) L.push("# " + String(snap.note).replace(/[\r\n]+/g, " "));
  L.push("# instruments," + Object.entries(S.sources)
    .map(([k, n]) => (WX.SOURCE_LABEL[k] || k) + " on " + n + " day(s)").join("; "));
  L.push("# normals,gridMET 1995-2025, " + S.normal_years_min + "-year or longer sample; % of normal compares gridMET with it on "
    + S.normal_covers_days + " of " + S.days_observed + " observed days");
  if (S.days_forecast) L.push("# forecast," + S.days_forecast
    + " forecast day(s) are included as rows and excluded from every total");
  if (view.filter && view.filter !== "all") L.push("# filter," + WX.FILTERS[view.filter].label);
  L.push("# generated," + data.generated_at);
  L.push([ "date", "is_forecast", "rain_in", "cumulative_in", "gridmet_in", "gridmet_to_date_in", "normal_to_date_in", "normal_day_in",
           "tmax_f", "tmin_f", "normal_tmax_f", "normal_tmin_f", "gdd", "vpd_kpa", "srad_wm2",
           "freeze", "source" ].map(q).join(","));
  for (const d of rows) {
    L.push([d.date, d.is_forecast ? "forecast" : "", d.precip_in, d.precip_cum_in, d.precip_gm_in, d.gm_cum_in, d.normal_cum_in,
            d.normal_precip_in, d.tmax_f, d.tmin_f, d.normal_tmax_f, d.normal_tmin_f, d.gdd,
            d.vpd_kpa, d.srad_wm2, d.freeze === true ? "yes" : d.freeze === false ? "no" : "",
            d.precip_source_label || d.precip_source || ""].map(q).join(","));
  }
  const name = "weather-record_" + (data.window.from || "") + "_" + (data.window.to || "")
    + "_cell" + snap.cell_id + ".csv";
  const blob = new Blob(["﻿" + L.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

// ── ABOVE / BELOW / NEAR NORMAL, PER METRIC ───────────────────────────────────
// Kevin, 2026-10-13: "Can we somehow quickly show whether the selected range is above
// or below or normal for each piece of data?" The arithmetic is `WX.compareToNormal`
// so the screen and the printed sheet cannot disagree; this only draws it.
//
// ⚠ A METRIC THAT CANNOT BE COMPARED STILL SHOWS ITS VALUE AND SAYS WHY. Three of the
// eight normals are published at fixed settings (GDD base 50°F, freeze ≤28°F, heat
// >90°F) while the page lets the reader move two of them. Blanking the row would hide
// a real measurement; differencing it anyway would subtract two different quantities.
const WX_STATE = {
  above: { word: "above normal", bg: "#fdf0e4", fg: "#8a4b00", bar: "#c87f2e" },
  below: { word: "below normal", bg: "#eaf1fb", fg: "#1f4b86", bar: "#4a7fc1" },
  near:  { word: "near normal",  bg: "#eef5ee", fg: "#2d6a2d", bar: "#5c9a5c" },
};
function WxNormalsStrip({ normals, years, isMobile }) {
  if (!normals || !normals.length) return null;
  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 12, padding: isMobile ? 12 : 14 };
  return (
    <div style={{ ...card, marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap", marginBottom: 9 }}>
        <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", color: "#60735f", fontWeight: 700 }}>
          Against normal for these dates
        </span>
        <span style={{ fontSize: 10.5, color: "#60735f" }}>
          {years ? `${years}-year normals · ` : ""}near normal = within {Math.round(WX.NEAR_BAND_RATIO * 100)}% ({WX.NEAR_BAND_F}°F on temperatures).
          That band is a reading aid, not a significance test — the stored normals carry a mean and a year count, no spread.
        </span>
      </div>
      <div style={{ display: "grid", gap: 7,
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(235px, 1fr))" }}>
        {normals.map((m) => {
          const st = m.state ? WX_STATE[m.state] : null;
          return (
            <div key={m.key} style={{ border: "1px solid #e8efe8", borderRadius: 9, padding: "7px 10px",
                                      background: st ? st.bg : "#f7f9f7" }}>
              <div style={{ fontSize: 11, color: "#5c6f5c", fontWeight: 600 }}>{m.label}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap", marginTop: 1 }}>
                <span style={{ fontSize: 17, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>
                  {m.value == null ? "—" : m.value}
                </span>
                <span style={{ fontSize: 11, color: "#60735f" }}>{m.unit}</span>
                {st ? (
                  <span style={{ fontSize: 11, fontWeight: 800, color: st.fg }}>
                    {m.departure > 0 ? "+" : ""}{m.departure} {st.word}
                  </span>
                ) : null}
              </div>
              {m.comparable && m.normal != null ? (
                <div style={{ fontSize: 10.5, color: "#60735f" }}>
                  normal {m.normal}{m.pct_of_normal != null ? ` · ${m.pct_of_normal}% of normal` : ""} · {m.days_compared} day{m.days_compared === 1 ? "" : "s"} compared
                  {m.note ? <span style={{ color: "#8a6100" }}> · {m.note}</span> : null}
                </div>
              ) : (
                <div style={{ fontSize: 10.5, color: "#8a6100" }}>
                  not compared — {m.reason}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── THE CHARTS ────────────────────────────────────────────────────────────────
// Kevin, 2026-10-13: "Can you make some cool graphs charts too?"
//
// ⚠ NO SECOND CHART COMPONENT. `WxChart` already exists for FieldWeatherView — inline
// SVG, no dependency, with bars, a second axis, high/low bands, reference lines and a
// hover tooltip — and the first cut of this work shipped a rival one before
// `check-undef` refused the duplicate name. This repo's record on a rule defined twice
// is that it drifts twice (three news handlers, two guarantee engines, three copies of
// normFarmerName), and a chart is a rule about how a number is drawn. These three are
// the same component fed from the Weather Records series.
//
// What IS specific here, and is the whole reason these charts are worth drawing:
//   ⚠ A FORECAST IS NOT AN OBSERVATION. `precip_cum_in` and `normal_cum_in` both stop
//     advancing on a forecast day, so the accumulation lines end where the measurement
//     ends; the forecast is shown as bars and labelled, never as part of the total.
//   ⚠ A MISSING READING IS A GAP, NOT A ZERO. `null` reaches the chart as null, and
//     WxChart breaks its line rather than drawing a day nobody measured as 0.
function WxCharts({ series = [], gddBase = 50, freezeF = 28, isMobile = false }) {
  const c = useMemo(() => {
    if (!series.length) return null;
    const dates = series.map((d) => d.date);
    const fc = series.map((d) => d.is_forecast === true);
    const hasNormal = series.some((d) => d.normal_precip_in != null);
    // Cumulative GDD, measured days only — the same rule buildSeries applies to rain.
    let g = 0, gn = 0;
    const cumGdd = [], cumGddNorm = [];
    for (const d of series) {
      const counts = !d.is_forecast && d.gdd != null;
      if (counts) g += d.gdd;
      if (counts && d.normal_gdd50 != null) gn += d.normal_gdd50;
      cumGdd.push(counts || g > 0 ? Math.round(g * 10) / 10 : null);
      cumGddNorm.push(gn > 0 ? Math.round(gn * 10) / 10 : null);
    }
    const lastObs = [...series].reverse().find((d) => !d.is_forecast && d.precip_cum_in != null);
    return {
      dates, fc, hasNormal, cumGdd, cumGddNorm, lastObs,
      precip: series.map((d) => d.precip_in),
      cumPrecip: series.map((d) => (d.is_forecast ? null : d.precip_cum_in)),
      normCum: series.map((d) => d.normal_cum_in),
      // gridMET's own running total — the line the normal is drawn against.
      gmCum: series.map((d) => (d.is_forecast ? null : d.gm_cum_in)),
      bands: series.map((d) => (d.tmax_f == null || d.tmin_f == null ? [null, null] : [d.tmin_f, d.tmax_f])),
      hi: series.map((d) => d.tmax_f), lo: series.map((d) => d.tmin_f),
      nHi: series.map((d) => d.normal_tmax_f), nLo: series.map((d) => d.normal_tmin_f),
      gdd: series.map((d) => d.gdd),
      gddNormalUsable: Number(gddBase) === WX.NORMAL_GDD_BASE_F,
    };
  }, [series, gddBase]);
  if (!c) return null;

  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 12, padding: isMobile ? 10 : 14 };
  const head = (t, sub) => (
    <div style={{ marginBottom: 2 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2e1a" }}>{t}</div>
      <div style={{ fontSize: 10.5, color: "#60735f" }}>{sub}</div>
    </div>
  );
  const f2 = (v) => (v == null ? "—" : `${(Math.round(v * 100) / 100).toFixed(2)}"`);

  return (
    <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
      <div style={card}>
        {head("Rainfall", c.lastObs
          ? `Daily (bars) and running total · ${f2(c.lastObs.precip_cum_in)} measured · gridMET ${f2(c.lastObs.gm_cum_in)} against ${f2(c.lastObs.normal_cum_in)} normal through ${c.lastObs.date}`
          : "Daily rainfall")}
        <WxChart days={c.dates} bars={c.precip}
          lines={[{ data: c.cumPrecip, color: "#1f5fb0", width: 2, axis2: true },
                  ...(c.hasNormal ? [{ data: c.gmCum, color: "#2d6a2d", width: 1.4, axis2: true },
                                     { data: c.normCum, color: "#677167", width: 1.6, axis2: true, dash: "5,3" }] : [])]}
          color="#5b9bd5" yLabel="in/day" y2Label="Total" height={isMobile ? 170 : 205}
          fmtY={(v) => v.toFixed(2)} fmtY2={(v) => v.toFixed(1) + '"'}
          tooltip={(i) => [
            { label: "Rain", value: f2(c.precip[i]), color: "#5b9bd5" },
            { label: "Total", value: f2(c.cumPrecip[i]), color: "#1f5fb0" },
            ...(c.hasNormal ? [{ label: "gridMET to date", value: f2(c.gmCum[i]), color: "#2d6a2d" },
                               { label: "Normal to date", value: f2(c.normCum[i]), color: "#677167" }] : []),
            ...(c.fc[i] ? [{ label: "—", value: "forecast, not in the total", color: "#8a6100" }] : []),
          ]} />
      </div>

      <div style={card}>
        {head("Temperature", `Daily high–low range against normal, with the ${freezeF}°F freeze line this window is set to`)}
        <WxChart days={c.dates} bands={c.bands}
          lines={[{ data: c.hi, color: "#b1442e", width: 1.5 }, { data: c.lo, color: "#1f5fb0", width: 1.5 },
                  ...(c.hasNormal ? [{ data: c.nHi, color: "#caa6a0", width: 1.3, dash: "4,3" },
                                      { data: c.nLo, color: "#9ab0c4", width: 1.3, dash: "4,3" }] : [])]}
          refLines={[{ v: freezeF, color: "#1f7fb0", label: `${freezeF}° freeze` },
                     { v: WX.NORMAL_HEAT_F, color: "#b1442e", label: `${WX.NORMAL_HEAT_F}° heat` }]}
          color="#d0954a" yLabel="°F" height={isMobile ? 180 : 210} fmtY={(v) => Math.round(v) + "°"}
          tooltip={(i) => [
            { label: "High", value: c.hi[i] == null ? "—" : `${c.hi[i]}°`, color: "#b1442e" },
            { label: "Low", value: c.lo[i] == null ? "—" : `${c.lo[i]}°`, color: "#1f5fb0" },
            ...(c.hasNormal ? [{ label: "Norm hi", value: c.nHi[i] == null ? "—" : `${Math.round(c.nHi[i])}°`, color: "#caa6a0" },
                               { label: "Norm lo", value: c.nLo[i] == null ? "—" : `${Math.round(c.nLo[i])}°`, color: "#9ab0c4" }] : []),
          ]} />
      </div>

      <div style={card}>
        {head("Growing degree days", c.gddNormalUsable
          ? `Daily (bars) with the accumulation against normal · base ${gddBase}°F`
          : `Daily (bars) with the accumulation · base ${gddBase}°F — the stored normal is published at base ${WX.NORMAL_GDD_BASE_F}°F, so no normal line is drawn`)}
        <WxChart days={c.dates} bars={c.gdd}
          lines={[{ data: c.cumGdd, color: "#2d6a1a", width: 2, axis2: true },
                  ...(c.hasNormal && c.gddNormalUsable
                      ? [{ data: c.cumGddNorm, color: "#677167", width: 1.6, axis2: true, dash: "5,3" }] : [])]}
          color="#86c95a" yLabel="GDD/day" y2Label="Total" height={isMobile ? 170 : 205}
          fmtY={(v) => Math.round(v)} fmtY2={(v) => Math.round(v).toLocaleString()}
          tooltip={(i) => [
            { label: "GDD", value: c.gdd[i] == null ? "—" : `${c.gdd[i]}`, color: "#86c95a" },
            { label: "Total", value: c.cumGdd[i] == null ? "—" : Math.round(c.cumGdd[i]).toLocaleString(), color: "#2d6a1a" },
            ...(c.hasNormal && c.gddNormalUsable
                ? [{ label: "Normal", value: c.cumGddNorm[i] == null ? "—" : Math.round(c.cumGddNorm[i]).toLocaleString(), color: "#677167" }] : []),
          ]} />
      </div>
    </div>
  );
}

function WeatherRecordsView({ farmers = [], isMobile = false, onNav = null, embedded = false }) {
  const [L, setL] = useState(null);
  const mapEl = useRef(null), mapRef = useRef(null), pinRef = useRef(null), fieldLayer = useRef(null);
  const [point, setPoint] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [clu, setClu] = useState("");
  const [farmerId, setFarmerId] = useState("");
  const [fields, setFields] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("date");
  const [dir, setDir] = useState("asc");
  const [freezeF, setFreezeF] = useState(28);
  const [gddBase, setGddBase] = useState(50);

  useEffect(() => { if (!L) loadLeaflet().then(setL).catch(() => {}); }, [L]);

  // ── COME BACK TO WHAT YOU WERE LOOKING AT ──────────────────────────────────
  // This is a tool you use repeatedly against the same farm during a claim, and
  // losing the pin every time you check a policy makes it useless for that. The
  // location and window are kept in sessionStorage (same place the app already
  // keeps `cloverag_nav`), and a restored point fetches on its own — arriving at
  // a page that remembers the pin but not the answer is the worst of both.
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    if (restored) return;
    setRestored(true);
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem("cloverag_wxrec") || "null"); } catch (e) { saved = null; }
    const w = WX_PRESETS[0].win();
    setFrom((saved && saved.from) || w[0]);
    setTo((saved && saved.to) || w[1]);
    if (saved && Number.isFinite(+saved.lat) && Number.isFinite(+saved.lon)) {
      setPoint({ lat: +saved.lat, lon: +saved.lon, label: saved.label || `${saved.lat}, ${saved.lon}` });
      setClu(saved.clu || "");
      if (saved.freezeF != null) setFreezeF(+saved.freezeF);
      if (saved.gddBase != null) setGddBase(+saved.gddBase);
    }
  }, [restored]);

  useEffect(() => {
    if (!point || !from || !to) return;
    try {
      sessionStorage.setItem("cloverag_wxrec", JSON.stringify({
        lat: point.lat, lon: point.lon, label: point.label, clu, from, to, freezeF, gddBase,
      }));
    } catch (e) {}
  }, [point, clu, from, to, freezeF, gddBase]);

  // A restored point reads its record once, without a click.
  const [autoRan, setAutoRan] = useState(false);
  useEffect(() => {
    if (autoRan || !restored || !point || !from || !to || data || busy) return;
    setAutoRan(true);
    look();
    // `look` is deliberately not a dependency: it closes over the same state this
    // effect already watches, and listing it would re-fire on every render.
  }, [autoRan, restored, point, from, to, data, busy]);

  useEffect(() => {
    if (!L || !mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { zoomControl: true, attributionControl: false }).setView([42.9, -77.6], 7);
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 18 }).addTo(map);
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 18, opacity: 0.9 }).addTo(map);
    map.on("click", (ev) => {
      const la = +ev.latlng.lat.toFixed(5), lo = +ev.latlng.lng.toFixed(5);
      // ⚠ A CLICKED POINT IS NOT A FIELD. Clearing the CLU is what stops field-exact
      // rainfall from one farm's boundary being printed under a point somewhere else.
      setPoint({ lat: la, lon: lo, label: la + ", " + lo }); setClu("");
    });
    mapRef.current = map;
    setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 60);
    return () => { try { map.remove(); } catch (e) {} mapRef.current = null; };
  }, [L]);

  useEffect(() => {
    const map = mapRef.current;
    if (!L || !map) return;
    if (pinRef.current) { try { map.removeLayer(pinRef.current); } catch (e) {} pinRef.current = null; }
    if (!point) return;
    pinRef.current = L.circleMarker([point.lat, point.lon],
      { radius: 7, color: "#fff", weight: 2, fillColor: "#c0392b", fillOpacity: 1 }).addTo(map);
    try { map.setView([point.lat, point.lon], Math.max(map.getZoom(), 10)); } catch (e) {}
  }, [L, point]);

  useEffect(() => {
    if (!farmerId) { setFields(null); return; }
    let live = true;
    authedFetch(`/api/market?fn=wx-evidence&op=fields&farmer=${encodeURIComponent(farmerId)}`)
      .then((r) => r.json()).then((j) => { if (live) setFields(j && j.ok ? j : null); })
      .catch(() => { if (live) setFields(null); });
    return () => { live = false; };
  }, [farmerId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!L || !map) return;
    if (fieldLayer.current) { try { map.removeLayer(fieldLayer.current); } catch (e) {} fieldLayer.current = null; }
    if (!fields || !fields.fields || !fields.fields.length) return;
    const grp = L.layerGroup();
    for (const f of fields.fields) {
      L.circleMarker([f.lat, f.lon], { radius: 5, color: "#2d6a2d", weight: 2, fillColor: "#8fd18f", fillOpacity: 0.95 })
        .bindTooltip(`${f.label}${f.acres ? ` · ${Math.round(f.acres)} ac` : ""}${f.crop ? ` · ${f.crop}` : ""}`)
        .on("click", () => { setPoint({ lat: f.lat, lon: f.lon, label: f.label }); setClu(f.clu_id); })
        .addTo(grp);
    }
    grp.addTo(map);
    fieldLayer.current = grp;
    try { map.fitBounds(fields.fields.map((f) => [f.lat, f.lon]), { padding: [30, 30], maxZoom: 12 }); } catch (e) {}
  }, [L, fields]);

  const look = async () => {
    if (!point) { setErr("Click the map to choose a location."); return; }
    setBusy(true); setErr(""); setData(null);
    try {
      const qs = `op=series&lat=${point.lat}&lon=${point.lon}&from=${from}&to=${to}`
        + `&freezeF=${freezeF}&gddBase=${gddBase}${clu ? `&clu=${encodeURIComponent(clu)}` : ""}`;
      const r = await authedFetch(`/api/market?fn=wx-evidence&${qs}`);
      const j = await r.json();
      if (!r.ok || !j || !j.ok) setErr((j && j.error) || "Couldn't read the record.");
      else setData({ ...j, point_label: point.label });
    } catch (e) { setErr("Couldn't read the record."); }
    setBusy(false);
  };

  const rows = useMemo(() => (data && data.series ? WX.applyView(data.series, { filter, sort, dir }) : []),
    [data, filter, sort, dir]);

  const S = data && data.summary;
  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 12, padding: isMobile ? 12 : 16 };
  const lbl = { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", color: "#60735f", fontWeight: 700 };
  const th = (k, text, right) => (
    <th onClick={() => { if (sort === k) setDir((d) => (d === "asc" ? "desc" : "asc")); else { setSort(k); setDir(k === "date" ? "asc" : "desc"); } }}
      style={{ cursor: "pointer", textAlign: right ? "right" : "left", padding: "5px 7px", fontSize: 10.5,
               color: sort === k ? "#1a2e1a" : "#60735f", fontWeight: 700, whiteSpace: "nowrap", borderBottom: "1px solid #e4ece4" }}>
      {text}{sort === k ? (dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
  const td = (v, right) => <td style={{ padding: "4px 7px", textAlign: right ? "right" : "left", fontSize: 11.5, fontVariantNumeric: "tabular-nums" }}>{v}</td>;

  return (
    <div style={{ maxWidth: 1380, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        {!embedded && <h2 style={{ ...uiPageTitle(isMobile) }}>Weather Records</h2>}
        <div style={{ fontSize: 12.5, color: "#5c6f5c", marginTop: 3 }}>
          What the weather did, at a point, over any window — for prevent plant and FSA supporting
          documents, freeze and drought. Admin only. For the season against normal, radar and fieldwork windows, use{" "}
          {/* Degrades to plain bold text without onNav — the pointer is still worth
              stating, and a button that navigates nowhere is worse than a word. */}
          {onNav
            ? <button onClick={() => onNav("rainwatch")}
                style={{ cursor: "pointer", fontFamily: "inherit", border: "none", background: "none", padding: 0,
                         fontSize: "inherit", fontWeight: 800, color: "#2d6a2d", textDecoration: "underline" }}>Weather</button>
            : <b>Weather</b>}.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div ref={mapEl} style={{ height: isMobile ? 280 : 420, width: "100%", background: "#eef2ee" }} />
          <div style={{ padding: "7px 11px", fontSize: 11, color: "#5c6f5c", borderTop: "1px solid #e8efe8" }}>
            Click anywhere to choose a location. Pick a farm to drop its fields as pins — a field pin
            also asks for that field&rsquo;s <b>field-exact</b> rainfall where we hold it.
          </div>
        </div>

        <div style={{ ...card, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <div style={lbl}>Location</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginTop: 2 }}>
              {point ? point.label : <span style={{ color: "#677167", fontWeight: 600 }}>click the map</span>}
            </div>
            {clu ? <div style={{ fontSize: 11, color: "#2d6a2d", fontWeight: 700 }}>field pin · field-exact rainfall requested</div> : null}
          </div>

          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
            <select value={farmerId} onChange={(e) => setFarmerId(e.target.value)}
              style={{ fontSize: 12, padding: "5px 7px", borderRadius: 6, border: "1px solid #d5e2d5", maxWidth: 230 }}>
              <option value="">drop a farm&rsquo;s fields…</option>
              {[...farmers].sort((a, b) => String(a.name).localeCompare(String(b.name)))
                .map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            {fields && fields.without_centroid > 0 ? (
              <span style={{ fontSize: 10.5, color: "#8a6100" }}
                title="These fields have no stored boundary centroid, so they cannot be placed on the map. They are not fields without weather.">
                {fields.without_centroid} field{fields.without_centroid === 1 ? "" : "s"} have no map point
              </span>
            ) : null}
          </div>

          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              style={{ fontSize: 12, padding: "5px 7px", borderRadius: 6, border: "1px solid #d5e2d5" }} />
            <span style={{ color: "#677167" }}>→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              style={{ fontSize: 12, padding: "5px 7px", borderRadius: 6, border: "1px solid #d5e2d5" }} />
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {WX_PRESETS.map((p) => (
              <button key={p.key} onClick={() => { const w = p.win(); setFrom(w[0]); setTo(w[1]); }}
                style={{ cursor: "pointer", border: "1px solid #dde5dd", background: "#fff", color: "#3f553f",
                         borderRadius: 6, padding: "4px 8px", fontSize: 11, fontWeight: 700 }}>{p.label}</button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", fontSize: 11.5, color: "#5c6f5c" }}>
            <label>Freeze at <input type="number" value={freezeF} onChange={(e) => setFreezeF(+e.target.value)}
              style={{ width: 54, fontSize: 11.5, padding: "3px 5px", borderRadius: 5, border: "1px solid #d5e2d5", textAlign: "right" }} />°F</label>
            <label>GDD base <input type="number" value={gddBase} onChange={(e) => setGddBase(+e.target.value)}
              style={{ width: 54, fontSize: 11.5, padding: "3px 5px", borderRadius: 5, border: "1px solid #d5e2d5", textAlign: "right" }} />°F</label>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={look} disabled={busy || !point}
              style={{ cursor: busy || !point ? "default" : "pointer", border: "none", borderRadius: 7, padding: "8px 16px",
                       fontSize: 12.5, fontWeight: 800, color: "#fff", background: busy || !point ? "#9bb39b" : "#2d6a2d" }}>
              {busy ? "reading…" : "Get the record"}
            </button>
            {data && data.series && data.series.length ? (
              <>
                <button onClick={() => WX_printRecord(data, { filter, sort, dir })}
                  style={{ cursor: "pointer", border: "1px solid #2d6a2d", background: "#fff", color: "#2d6a2d",
                           borderRadius: 7, padding: "7px 13px", fontSize: 12, fontWeight: 800 }}>🖨 Print</button>
                {/* Both exports carry the SAME filter and sort the table is showing — a
                    sheet that silently prints all 71 days when the screen shows the 24 wet
                    ones is a different document from the one the agent was reading. */}
                <button onClick={() => WX_exportCsv(data, { filter, sort, dir })}
                  style={{ cursor: "pointer", border: "1px solid #2d6a2d", background: "#fff", color: "#2d6a2d",
                           borderRadius: 7, padding: "7px 13px", fontSize: 12, fontWeight: 800 }}>⬇ Export CSV</button>
              </>
            ) : null}
          </div>
          {err ? <div style={{ fontSize: 12, color: "#a5461b" }}>{err}</div> : null}
        </div>
      </div>

      {data && data.snap ? (
        <div style={{ ...card, marginTop: 12, padding: "10px 14px",
                      background: data.snap.ok ? (data.snap.far ? "#fdf6e8" : "#f4f9f4") : "#fdf0ec",
                      borderColor: data.snap.ok ? (data.snap.far ? "#e8d9ae" : "#d9e8d9") : "#efcfc4" }}>
          {data.snap.ok ? (
            <div style={{ fontSize: 12, color: data.snap.far ? "#7a5a10" : "#3f553f" }}>
              <b>Grid cell {data.snap.cell_id}</b> · {data.snap.county} County · {data.snap.distance_mi} mi from the point chosen
              {data.snap.far ? <b> — check this is the right place</b> : null}
              <div style={{ marginTop: 2, fontSize: 11 }}>{data.snap.note}</div>
            </div>
          ) : <div style={{ fontSize: 12, color: "#8c3a1e" }}>{data.snap.reason}</div>}
        </div>
      ) : null}

      {S ? (
        <>
          <div style={{ display: "grid", gap: 10, marginTop: 12,
                        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, minmax(0,1fr))" }}>
            <div style={card}>
              <div style={lbl}>Rainfall</div>
              <div style={{ fontSize: 23, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{S.precip_in}&quot;</div>
              {S.normal_precip_in != null ? (
                <div style={{ fontSize: 11.5, fontWeight: 700, color: S.precip_pct_normal >= 100 ? "#2d6a2d" : "#8a6100" }}>
                  {S.precip_pct_normal}% of normal · {S.precip_departure_in >= 0 ? "+" : ""}{S.precip_departure_in}&quot;
                </div>
              ) : <div style={{ fontSize: 11.5, color: "#8a6100" }}>no normal for this window</div>}
              <div style={{ fontSize: 10.5, color: "#60735f" }}>
                gridMET {S.precip_gm_in}&quot; vs normal {S.normal_precip_in}&quot; over {S.normal_covers_days} days
                {S.normal_years_min != null ? ` · ${S.normal_years_min}+ yr sample` : ""}
              </div>
            </div>
            <div style={card}>
              <div style={lbl}>Wet days</div>
              <div style={{ fontSize: 23, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{S.wet_days}</div>
              <div style={{ fontSize: 11.5, color: "#5c6f5c" }}>days ≥ 0.10&quot; · heaviest {S.max_day_in}&quot;</div>
              {S.longest_dry_run ? (
                <div style={{ fontSize: 10.5, color: "#8a6100" }}>
                  longest dry run {S.longest_dry_run.days} d ({S.longest_dry_run.from} → {S.longest_dry_run.to})
                </div>
              ) : null}
            </div>
            <div style={card}>
              <div style={lbl}>Cold</div>
              <div style={{ fontSize: 23, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{S.coldest_f}°F</div>
              <div style={{ fontSize: 11.5, color: "#5c6f5c" }}>
                {S.freeze_days} day{S.freeze_days === 1 ? "" : "s"} ≤ {S.freeze_threshold_f}°F
                {S.freeze_events.length ? ` · ${S.freeze_events.length} event${S.freeze_events.length === 1 ? "" : "s"}` : ""}
              </div>
              {S.freeze_events.slice(0, 2).map((e, i) => (
                <div key={i} style={{ fontSize: 10.5, color: "#60735f" }}>
                  {e.from}{e.days > 1 ? ` → ${e.to}` : ""} · {e.days} d · low {e.min_f}°F
                </div>
              ))}
            </div>
            <div style={card}>
              <div style={lbl}>Heat</div>
              <div style={{ fontSize: 23, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{S.gdd}</div>
              <div style={{ fontSize: 11.5, color: "#5c6f5c" }}>GDD base {gddBase}°F · hottest {S.hottest_f}°F</div>
              <div style={{ fontSize: 10.5, color: "#60735f" }}>
                {S.days_observed} observed day{S.days_observed === 1 ? "" : "s"}
                {S.days_forecast ? ` · ${S.days_forecast} forecast, not counted` : ""}
              </div>
            </div>
          </div>

          <div style={{ fontSize: 11, color: "#5c6f5c", margin: "9px 2px 0" }}>
            Measured by{" "}
            {Object.entries(S.sources).map(([k, n], i) => (
              <span key={k}>{i ? " · " : ""}<b>{WX.SOURCE_LABEL[k] || k}</b> on {n} day{n === 1 ? "" : "s"}</span>
            ))}
            {data.field_rainfall && data.field_rainfall.days === 0 ? (
              <span style={{ color: "#8a6100" }}> · field-exact rainfall not held for this window — {data.field_rainfall.reason}</span>
            ) : null}
          </div>

          <WxNormalsStrip normals={data.normals} years={S.normal_years_min} isMobile={isMobile} />
          <WxCharts series={data.series} gddBase={gddBase} freezeF={freezeF} isMobile={isMobile} />

          <div style={{ ...card, marginTop: 12 }}>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 9, alignItems: "center" }}>
              <span style={lbl}>Show</span>
              {Object.keys(WX.FILTERS).map((k) => (
                <button key={k} onClick={() => setFilter(k)}
                  style={{ cursor: "pointer", borderRadius: 6, padding: "4px 9px", fontSize: 11, fontWeight: 700,
                           border: filter === k ? "1px solid #2d6a2d" : "1px solid #dde5dd",
                           background: filter === k ? "#2d6a2d" : "#fff", color: filter === k ? "#fff" : "#5c6f5c" }}>
                  {WX.FILTERS[k].label}
                </button>
              ))}
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#60735f" }}>
                {rows.length} of {data.series.length} days
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 760 }}>
                <thead><tr>
                  {th("date", "Date")}
                  {th("precip_in", "Rain\"", true)}
                  {th("precip_cum_in", "Cumulative", true)}
                  {/* ⚠ CUMULATIVE, BESIDE THE CUMULATIVE IT IS MEANT TO BE READ
                      AGAINST. Kevin, 2026-10-13: "normal here is also confusing
                      because its not cumulative, what is normal rainfall for selected
                      range?" The daily normal runs 0.06–0.20" while the actual beside
                      it had reached 4.95", so the eye compared a season against a day.
                      The last row of this column is the answer to his question. */}
                  {th("gm_cum_in", "gridMET to date", true)}
                  {th("normal_cum_in", "Normal to date", true)}
                  {th("tmax_f", "Max °F", true)}
                  {th("tmin_f", "Min °F", true)}
                  {th("gdd", "GDD", true)}
                  {th("vpd_kpa", "VPD", true)}
                  {th("srad_wm2", "Solar", true)}
                  <th style={{ textAlign: "left", padding: "5px 7px", fontSize: 10.5, color: "#60735f", fontWeight: 700, borderBottom: "1px solid #e4ece4" }}>Source</th>
                </tr></thead>
                <tbody>
                  {rows.map((d) => (
                    <tr key={d.date} style={{ background: d.freeze ? "#eef4fb" : (d.precip_in >= 1 ? "#f4f9f4" : undefined) }}>
                      {td(<span>{d.date}{d.is_forecast ? <b style={{ color: "#8a6100" }}> forecast</b> : null}</span>)}
                      {td(d.precip_in == null ? "—" : d.precip_in.toFixed(2), true)}
                      {td(d.precip_cum_in == null ? "—" : d.precip_cum_in.toFixed(2), true)}
                      {td(d.gm_cum_in == null ? "—" : d.gm_cum_in.toFixed(2), true)}
                      {td(d.normal_cum_in == null ? "—" : d.normal_cum_in.toFixed(2), true)}
                      {td(d.tmax_f == null ? "—" : d.tmax_f, true)}
                      {td(d.tmin_f == null ? "—" : d.tmin_f, true)}
                      {td(d.gdd == null ? "—" : d.gdd, true)}
                      {td(d.vpd_kpa == null ? "—" : d.vpd_kpa, true)}
                      {td(d.srad_wm2 == null ? "—" : d.srad_wm2, true)}
                      <td style={{ padding: "4px 7px", fontSize: 10.5, color: "#60735f" }}>{d.precip_source || "—"}</td>
                    </tr>
                  ))}
                  {!rows.length ? <tr><td colSpan={10} style={{ padding: 14, fontSize: 12, color: "#60735f" }}>No days match this filter.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}

      {data && data.coverage ? (
        <div style={{ fontSize: 11, color: "#60735f", margin: "12px 2px 0", lineHeight: 1.6 }}>
          <b>What this can answer.</b> {data.coverage.states} {data.coverage.grid.note}{" "}
          {data.coverage.county.note} {data.coverage.field_precip.note}{" "}
          Wind, humidity and snow are not stored for past days anywhere in this system.
        </div>
      ) : null}
    </div>
  );
}

// A GROWER'S OWN GROUND, AS WEATHER SPOTS. A customer opening Weather for the first
// time was told to "add a field" while holding a hundred mapped fields. This turns
// their policy_fields rows into one suggestion per county: the MEDIAN field
// coordinate (a mean lands between two blocks of ground, in somebody else's woods),
// the field count and the acres, largest county first. Only the latest crop year
// present is used, so a field dropped since last year does not pull the point.
// Rows with no coordinate are skipped and nothing is invented for them.
function weatherSpotsFromFields(rows, max = 4) {
  const ok = (rows || []).filter((r) => Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude))
    && r.latitude != null && r.longitude != null && Number(r.latitude) !== 0);
  if (!ok.length) return [];
  const yr = Math.max(...ok.map((r) => Number(r.crop_year) || 0));
  const by = new Map();
  for (const r of ok) {
    if ((Number(r.crop_year) || 0) !== yr) continue;
    const c = String(r.county || "").trim().toUpperCase() || "YOUR";
    if (!by.has(c)) by.set(c, { county: c, lats: [], lons: [], acres: 0 });
    const g = by.get(c);
    g.lats.push(Number(r.latitude)); g.lons.push(Number(r.longitude)); g.acres += Number(r.acres) || 0;
  }
  const med = (a) => { const b = [...a].sort((x, y) => x - y), m = b.length >> 1; return b.length % 2 ? b[m] : (b[m - 1] + b[m]) / 2; };
  const title = (c) => c.toLowerCase().replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
  return [...by.values()]
    .map((g) => ({ name: `${title(g.county)} fields`, county: g.county, lat: +med(g.lats).toFixed(4), lon: +med(g.lons).toFixed(4),
                   nFields: g.lats.length, acres: Math.round(g.acres) }))
    .sort((a, b) => b.acres - a.acres || b.nFields - a.nFields)
    .slice(0, max);
}

// ─────────────────────────────────────────────────────────────────────────────
// WEATHER, ONE PAGE (Kevin, 2026-11-01: "Weather redesign looks great, go forwards
// with that"; and on folding Weather Records in: "Yes"). One page for farmers and
// agents, opened on a farm's own insured counties:
//   Season          how the season's rain and heat ran against normal, with the
//                   crop's own RMA planting dates drawn on (src/weather/wxSeason.js)
//   Radar and alerts the old Weather page, unchanged: saved spots, radar, alerts
//   Claim report    admin only: the old Weather Records page, unchanged. The server
//                   refuses its data to anyone else; the tab is not even offered.
// Claim days, PRF and the week ahead join as their own tabs in later stages.
// Rules: read only, every number names its source, a farmer sees only their own
// farm, no dollar figures. Key stays `rainwatch`; `wxrecords` opens the Claim report.
// ─────────────────────────────────────────────────────────────────────────────
const WXS_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const wxsMD = (iso) => { if (!iso) return "—"; const m = +iso.slice(5, 7), d = +iso.slice(8, 10); return `${WXS_MONTHS[m - 1]} ${d}`; };
const wxsIn = (v) => (v == null ? "—" : `${Number(v).toFixed(2)} in`);
const wxsTitle = (s) => String(s || "").toLowerCase().replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
const wxsCrop = (c) => wxsTitle(c || "crop");
const WXS_GRID_STATES = ["36", "42"];        // the season grid covers NY and PA today
const WXS_BAND = { plant: "rgba(47,125,50,0.10)", late: "rgba(192,138,44,0.14)" };

function wxsTodayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const WXS_PRIOR = "#9a8ab8";
function WxsChart({ days, win, isMobile, prior = null, priorYearLabel = null }) {
  const [hover, setHover] = useState(null);
  const W = 720, H = isMobile ? 240 : 260, L = 40, R = 14, TOP = 14, B = 26;
  if (!days || !days.length) return null;
  const n = days.length;
  const x = (i) => L + (n <= 1 ? 0 : (i / (n - 1)) * (W - L - R));
  // The season before, matched by month and day so a leap day never shifts it.
  const pBy = new Map((prior || []).map((d) => [d.date.slice(5), d]));
  const pAt = (d) => pBy.get(d.date.slice(5)) || (d.date.slice(5) === "02-29" ? pBy.get("02-28") : null) || null;
  const maxY = Math.max(1, ...days.map((d) => Math.max(d.rain_cum_in || 0, d.normal_cum_in || 0, (pAt(d) || {}).rain_cum_in || 0))) * 1.08;
  const y = (v) => TOP + (1 - v / maxY) * (H - TOP - B);
  const step = maxY > 24 ? 5 : maxY > 10 ? 2 : 1;
  const ticks = []; for (let v = 0; v <= maxY; v += step) ticks.push(v);
  const idxOf = (iso) => { if (!iso) return null; const i = days.findIndex((d) => d.date >= iso); return i < 0 ? (iso > days[n - 1].date ? n - 1 : null) : i; };
  const path = (k) => days.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[k] || 0).toFixed(1)}`).join(" ");
  const months = days.map((d, i) => (d.date.slice(8) === "01" ? { i, label: WXS_MONTHS[+d.date.slice(5, 7) - 1] } : null)).filter(Boolean);
  const band = (a, b, fill, key) => {
    const ia = idxOf(a), ib = idxOf(b);
    if (ia == null || ib == null || ib < ia) return null;
    return <rect key={key} x={x(ia)} y={TOP} width={Math.max(1, x(ib) - x(ia))} height={H - TOP - B} fill={fill} />;
  };
  const vline = (iso, color, key) => { const i = idxOf(iso); return i == null || days[i].date !== iso ? null :
    <line key={key} x1={x(i)} x2={x(i)} y1={TOP} y2={H - B} stroke={color} strokeWidth="1.5" strokeDasharray="3 3" />; };
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    setHover(Math.max(0, Math.min(n - 1, Math.round(((px - L) / (W - L - R)) * (n - 1)))));
  };
  const h = hover != null ? days[hover] : days[n - 1];
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block", touchAction: "pan-y" }}
        onMouseMove={onMove} onMouseLeave={() => setHover(null)} onClick={onMove}
        role="img" aria-label="Season rain to date against normal">
        {win && band(win.earliest_planting_date || days[0].date, win.final_planting_date, WXS_BAND.plant, "bp")}
        {win && band(win.final_planting_date, win.end_of_late_planting_date, WXS_BAND.late, "bl")}
        {ticks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={T.lineSoft} />
            <text x={L - 6} y={y(v) + 3.5} textAnchor="end" fontSize="10" fill={T.faint}>{v}</text>
          </g>
        ))}
        {months.map((m) => (
          <text key={m.i} x={x(m.i) + 3} y={H - 8} fontSize="10.5" fill={T.faint}>{m.label}</text>
        ))}
        {win && vline(win.final_planting_date, "#2f7d32", "vf")}
        {win && vline(win.end_of_late_planting_date, "#c08a2c", "vl")}
        <path d={path("normal_cum_in")} fill="none" stroke="#8a978a" strokeWidth="2" strokeDasharray="5 4" />
        {prior && prior.length > 0 && <path d={days.map((d, i) => { const p = pAt(d); return p ? `${i && pAt(days[i - 1]) ? "L" : "M"}${x(i).toFixed(1)},${y(p.rain_cum_in || 0).toFixed(1)}` : ""; }).join(" ")}
          fill="none" stroke={WXS_PRIOR} strokeWidth="1.8" />}
        <path d={path("rain_cum_in")} fill="none" stroke={WXV_TONE.rain[0]} strokeWidth="2.6" />
        {win && (() => { const i = idxOf(win.earliest_planting_date); return i == null || !win.earliest_planting_date ? null :
          <text x={x(i) + 3} y={TOP + 11} fontSize="9.5" fontWeight="700" fill={WXV_TONE.good[0]}>Earliest {wxsMD(win.earliest_planting_date)}</text>; })()}
        {win && (() => { const i = idxOf(win.final_planting_date); return i == null ? null :
          <text x={x(i) + 3} y={TOP + 23} fontSize="9.5" fontWeight="700" fill={WXV_TONE.warn[0]}>Final {wxsMD(win.final_planting_date)}</text>; })()}
        {(() => { const d = days[n - 1]; return d && d.rain_cum_in != null ? <g>
          <circle cx={x(n - 1)} cy={y(d.rain_cum_in)} r="4" fill={WXV_TONE.rain[0]} />
          <text x={x(n - 1) - 6} y={y(d.rain_cum_in) - 8} textAnchor="end" fontSize="11" fontWeight="800" fill={WXV_TONE.rain[0]}>{d.rain_cum_in.toFixed(1)}"</text></g> : null; })()}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={TOP} y2={H - B} stroke={T.ink} strokeOpacity="0.25" />}
        {hover != null && <circle cx={x(hover)} cy={y(h.rain_cum_in || 0)} r="3.5" fill={WXV_TONE.rain[0]} />}
      </svg>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", fontSize: 12, color: T.body, marginTop: 6, ...uiNum }}>
        <span><b style={{ color: T.ink }}>{wxsMD(h.date)}</b></span>
        <span>That day {h.counted ? wxsIn(h.rain_in) : "no reading"}</span>
        <span>Since {wxsMD(days[0].date)} <b style={{ color: WXV_TONE.rain[0] }}>{wxsIn(h.rain_cum_in)}</b></span>
        <span>Normal {wxsIn(h.normal_cum_in)}</span>
        {prior && pAt(h) && <span style={{ color: WXS_PRIOR }}>{priorYearLabel} {wxsIn(pAt(h).rain_cum_in)}</span>}
      </div>
    </div>
  );
}

function WxsMonthBars({ months }) {
  if (!months || !months.length) return null;
  // Each month as a percent of its normal, drawn from the 100% line: blue above,
  // amber below. A month still running is paler and says "so far".
  const W = 380, H = 150, L = 34, R = 8, TOP = 16, B = 22, MAX = 200;
  const bw = (W - L - R) / months.length;
  const y = (v) => TOP + (1 - Math.min(Math.max(v, 0), MAX) / MAX) * (H - TOP - B);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label="Monthly rain as a percent of normal">
      {[0, 50, 100, 150, 200].map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={v === 100 ? T.inkSoft : T.lineSoft} strokeDasharray={v === 100 ? "4 3" : undefined} />
          <text x={L - 5} y={y(v) + 3} textAnchor="end" fontSize="9.5" fill={T.faint}>{v}%</text>
        </g>
      ))}
      {months.map((m, i) => {
        const x = L + i * bw + bw * 0.2, w = bw * 0.6, lab = WXS_MONTHS[+m.month.slice(5, 7) - 1];
        if (m.pct == null) return <text key={m.month} x={x + w / 2} y={H - 6} textAnchor="middle" fontSize="10" fill={T.faint}>{lab}</text>;
        const top = y(m.pct), base = y(100), wet = m.pct >= 100, c = wet ? WXV_TONE.rain[0] : WXV_TONE.warn[0];
        return (
          <g key={m.month}>
            <title>{`${lab}: ${wxsIn(m.rain_in)} against ${wxsIn(m.normal_in)} normal${m.partial ? ", so far" : ""}`}</title>
            <rect x={x} y={Math.min(top, base)} width={w} height={Math.max(1, Math.abs(base - top))} rx="3" fill={c} fillOpacity={m.partial ? 0.45 : 0.85} />
            <text x={x + w / 2} y={wet ? top - 4 : top + 12} textAnchor="middle" fontSize="10" fontWeight="800" fill={c}>{m.pct}%</text>
            <text x={x + w / 2} y={H - 6} textAnchor="middle" fontSize="10" fill={T.faint}>{lab}{m.partial ? "*" : ""}</text>
          </g>
        );
      })}
    </svg>
  );
}

const wxsChip = (on) => ({ cursor: "pointer", font: "inherit", padding: "7px 14px", borderRadius: 999,
  border: `1px solid ${on ? "#2d4a2d" : T.line}`, background: on ? "#2d4a2d" : T.surface,
  color: on ? "#fff" : T.body, fontSize: 12.5, fontWeight: on ? 800 : 600 });

// ── The Weather look (the Nov 1 mockup Kevin picked) ──────────────────────────
// One big number with a pill, a few facts under it, a segmented tab bar, an icon
// per event, and the measuring notes folded behind one tap instead of a paragraph
// under every card ("people will never read big blocks of text").
const WXV_TONE = {
  rain:  ["#1f5fb0", "#e7f0fa"], good: ["#2f7d32", "#e8f4e6"], heat: ["#b1442e", "#f8e9e4"],
  frost: ["#3b7fa8", "#e4f1f7"], warn: ["#8a5e10", "#fbf1dc"], weak: ["#8c3a3a", "#f8e9e9"],
  plain: ["#2d4a2d", "#f7faf7"],
};
// Rain or an index as a percent of normal: wet, near normal or dry, the same cut
// the page has always coloured by (125 / 75).
const wxvPctTone = (p) => (p == null ? "plain" : p >= 125 ? "rain" : p <= 75 ? "warn" : "good");
function WxPill({ tone = "plain", children }) {
  const [c, bg] = WXV_TONE[tone] || WXV_TONE.plain;
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800, borderRadius: 20, padding: "3px 10px", color: c, background: bg, whiteSpace: "nowrap", ...uiNum }}>{children}</span>;
}
function WxHero({ eyebrow, value, unit, pill, pillTone, line, stats = [], isMobile }) {
  const st = stats.filter(Boolean);
  return (
    <div style={{ ...uiCard(14), marginBottom: 12, display: "grid", gap: 8 }}>
      {eyebrow && <div style={uiLabel}>{eyebrow}</div>}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontFamily: T.serif, fontSize: isMobile ? 36 : 42, fontWeight: 700, color: T.ink, lineHeight: 1, ...uiNum }}>
          {value}{unit && <span style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 700, color: T.faint, marginLeft: 5 }}>{unit}</span>}
        </div>
        {pill && <WxPill tone={pillTone}>{pill}</WxPill>}
      </div>
      {line && <div style={{ fontSize: 12.5, color: T.muted }}>{line}</div>}
      {st.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : `repeat(${Math.min(4, st.length)}, minmax(0,1fr))`, gap: 8 }}>
          {st.map((x, i) => (
            <div key={i} style={{ background: T.surfaceAlt, borderRadius: 10, padding: "8px 10px", minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: x.tone || T.ink, ...uiNum }}>{x.v}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.faint, lineHeight: 1.35 }}>{x.l}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function WxTabs({ tabs, value, onChange }) {
  return (
    <div role="tablist" style={{ display: "flex", gap: 4, background: T.surfaceAlt, border: `1px solid ${T.line}`, borderRadius: 12, padding: 4, marginBottom: 14, overflowX: "auto" }}>
      {tabs.map((t) => {
        const on = t.k === value;
        return (
          <button key={t.k} role="tab" aria-selected={on} onClick={() => onChange(t.k)}
            style={{ flex: "1 0 auto", font: "inherit", fontSize: 12.5, fontWeight: 700, border: 0, borderRadius: 9, padding: "8px 12px", minHeight: 36, cursor: "pointer", whiteSpace: "nowrap",
                     background: on ? T.surface : "transparent", color: on ? T.ink : T.faint, boxShadow: on ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>{t.label}</button>
        );
      })}
    </div>
  );
}
function WxNote({ children, label = "How this is measured" }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{ border: "none", background: "none", padding: "4px 0", cursor: "pointer", font: "inherit", fontSize: 12, fontWeight: 700, color: T.muted }}>
        {open ? "Hide notes" : label} {open ? "\u25B4" : "\u25BE"}
      </button>
      {open && <div style={{ fontSize: 11.5, color: T.faint, lineHeight: 1.6, marginTop: 4, maxWidth: 780 }}>{children}</div>}
    </div>
  );
}
// One icon per kind of weather event, in the event's own colour.
const WXV_ICON = {
  rain:  "M12 4c3 4 5 6.5 5 9a5 5 0 0 1-10 0c0-2.5 2-5 5-9z",
  frost: "M12 3v18M5 7l14 10M19 7L5 17",
  heat:  "M12 3v10a4 4 0 1 0 0.01 0",
  dry:   "M4 18h16M7 14c2-3 8-3 10 0",
  warn:  "M12 7v6M12 16v1",
};
function WxIcon({ kind, tone }) {
  const [c, bg] = WXV_TONE[tone] || WXV_TONE.plain;
  return (
    <span style={{ width: 30, height: 30, borderRadius: 9, background: bg, display: "grid", placeItems: "center", flexShrink: 0 }}>
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d={WXV_ICON[kind] || WXV_ICON.warn} fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" /></svg>
    </span>
  );
}

// The Season tab. Farm -> its insured counties (policy_fields, the farmer's own rows)
// -> each county's gridMET cells, daily readings and normals -> wxSeason's rules.
function WxSeasonTab({ farmers = [], isMobile = false, isPortal = false, farmKey, setFarmKey, view = "season", onOpenReport = null }) {
  const [year, setYear] = useState(CURRENT_CROP_YEAR);
  const [fields, setFields] = useState(null);
  const [county, setCounty] = useState("");
  const [cropCode, setCropCode] = useState("");
  const [wx, setWx] = useState(null);           // { key, cells, rows, norms, rma } for one county + year
  const [err, setErr] = useState("");

  const farmList = useMemo(() => {
    const seen = new Map();
    for (const f of farmers || []) {
      if (!f || !f.id) continue;
      // allFarmerDbIds: every farmer row merged into this card (the loader strips
      // `_allFarmerIds` once it has built it), so a merged farm reads all its fields.
      const ids = [...new Set([f.id, ...(f.allFarmerDbIds || f._allFarmerIds || [])].filter(Boolean))];
      const k = String(f.id);
      if (!seen.has(k)) seen.set(k, { key: k, name: f.name || "Farm", ids, prf: f.prfPolicies || [] });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [farmers]);
  // The farm last picked in this browser tab may not be one of these farms: an agent
  // previewing a farmer in the same tab, or a farmer who switched operations. Never
  // let that key strand the page on "Pick a farm"; a farmer with one farm lands on it.
  useEffect(() => {
    if (!farmList.length) return;
    const known = farmKey && farmList.some((f) => f.key === farmKey);
    if (known) return;
    const next = farmList.length === 1 ? farmList[0].key : "";
    if (next !== (farmKey || "")) setFarmKey(next);
  }, [farmKey, farmList, setFarmKey]);
  const farm = farmList.find((f) => f.key === farmKey) || (farmList.length === 1 ? farmList[0] : null);
  // The week ahead is always this year's: the counties come from this year's fields.
  useEffect(() => { if (view === "next" && year !== CURRENT_CROP_YEAR) setYear(CURRENT_CROP_YEAR); }, [view, year]);

  // The farm's insured fields for the season. The farmer filter is ALWAYS sent: RLS
  // scopes a farmer's own session, but an agent's session reads the whole book.
  useEffect(() => {
    let alive = true;
    setFields(null); setErr("");
    if (!farm) return undefined;
    sbFetch("policy_fields", { select: "farmer_id,state_fips,county_fips,county,rma_crop_code,crop,type_code,acres,latitude,longitude,clu_id,report_date",
      filters: [`farmer_id=in.(${farm.ids.join(",")})`, `crop_year=eq.${year}`] })
      .then((r) => { if (alive) setFields(r || []); })
      .catch((e) => { if (alive) { setFields([]); setErr(String((e && e.message) || e)); } });
    return () => { alive = false; };
  }, [farm && farm.key, year]);

  // John Deere's first planting pass on each of the farm's CLUs this spring, to date a
  // field the acreage report left blank. Agent sessions only: jd_field_ftf is not a
  // farmer read, so the portal goes by the acreage report alone. The farmer filter is
  // always sent, so a custom operator's pass on someone else's farm is never read.
  const [jdPlant, setJdPlant] = useState(null);   // { key, map: Map(WXS.jdKey -> first date) }
  useEffect(() => {
    let alive = true;
    setJdPlant(null);
    if (!farm || isPortal) return undefined;
    const key = `${farm.key}|${year}`;
    sbFetch("jd_field_ftf", { select: "clu_id,crop,operation_date",
      filters: [`farmer_id=in.(${farm.ids.join(",")})`, "metric=eq.seed_rate", `operation_date=gte.${year}-03-01`, `operation_date=lte.${year}-07-31`] })
      .then((rows) => {
        if (!alive) return;
        const map = new Map();
        for (const r of rows || []) {
          if (r.clu_id == null || !r.operation_date) continue;
          const k = WXS.jdKey(r.clu_id, r.crop), d = String(r.operation_date).slice(0, 10);
          if (!map.has(k) || d < map.get(k)) map.set(k, d);
        }
        setJdPlant({ key, map });
      })
      .catch(() => { if (alive) setJdPlant({ key, map: new Map() }); });
    return () => { alive = false; };
  }, [farm && farm.key, year, isPortal]);

  const counties = useMemo(() => {
    const by = new Map();
    for (const f of fields || []) {
      if (!f.state_fips || !f.county_fips) continue;
      const k = `${f.state_fips}${f.county_fips}`;
      if (!by.has(k)) by.set(k, { key: k, st: String(f.state_fips), co: String(f.county_fips), name: wxsTitle(f.county), acres: 0, rows: [] });
      const c = by.get(k);
      c.acres += Number(f.acres) || 0;
      c.rows.push(f);
    }
    return [...by.values()].sort((a, b) => b.acres - a.acres || a.name.localeCompare(b.name));
  }, [fields]);
  const inGrid = counties.filter((c) => WXS_GRID_STATES.includes(c.st));
  const outGrid = counties.filter((c) => !WXS_GRID_STATES.includes(c.st));
  useEffect(() => {
    if (!inGrid.length) { if (county) setCounty(""); return; }
    if (!inGrid.some((c) => c.key === county)) setCounty(inGrid[0].key);
  }, [inGrid.map((c) => c.key).join(","), county]);
  const cty = inGrid.find((c) => c.key === county) || null;

  const today = wxsTodayIso();
  const bounds = WXS.seasonBounds(year, today);

  // The Season view's own date range (Kevin, Nov 3: "select start dates"). Events and
  // the hard freeze always read the whole season; only the Season view's hero, chart
  // and months follow this. Planted and RMA starts stay inside the season fetch;
  // only a custom range can reach outside it, back to Jan 1 of the year.
  const [rng, setRng] = useState({ mode: "season", from: "", to: "" });
  const [cmp, setCmp] = useState(false);          // draw the season before on the same dates
  useEffect(() => { setRng({ mode: "season", from: "", to: "" }); setCmp(false); }, [farm && farm.key, year]);
  const custom = WXS.clampRange(rng.from, rng.to, year, today);
  const fetchFrom = rng.mode === "custom" && custom && bounds.from && custom.from < bounds.from ? custom.from : bounds.from;
  const fetchTo = rng.mode === "custom" && custom && bounds.to && custom.to > bounds.to ? custom.to : bounds.to;

  // One county's weather and RMA dates for the season.
  useEffect(() => {
    let alive = true;
    if (!cty || !bounds.to) { setWx(null); return undefined; }
    const key = `${cty.key}|${year}|${fetchFrom}|${fetchTo}`;
    setWx(null); setErr("");
    const crops = [...new Set(cty.rows.map((r) => String(r.rma_crop_code || "")).filter((c) => c && c !== "0088"))];
    const rmaP = crops.length ? sbFetch("rma_dates", {
      select: "commodity_code,type_code,practice_code,insurance_plan_code,earliest_planting_date,final_planting_date,extended_final_planting_date,end_of_late_planting_date",
      filters: [`crop_year=eq.${year}`, `state_code=eq.${cty.st}`, `county_code=eq.${cty.co}`,
                `commodity_code=in.(${crops.join(",")})`, "insurance_plan_code=in.(01,02,03,90)"] }) : Promise.resolve([]);
    sbFetch("wny_grid_cells", { select: "id,lat,lon,frost28_doy_med", filters: [`county_fips=eq.${cty.key}`] })
      .then((cells) => {
        if (!alive) return null;
        if (!cells || !cells.length) { setWx({ key, cells: [], rows: [], norms: [], rma: [] }); return null; }
        const ids = cells.map((c) => c.id).join(",");
        return Promise.all([
          sbFetch("wny_grid_weather_daily", { select: "cell_id,obs_date,tmax_c,tmin_c,precip_mm,precip_gridmet_mm,source,is_forecast",
            filters: [`cell_id=in.(${ids})`, `obs_date=gte.${fetchFrom}`, `obs_date=lte.${fetchTo}`, "is_forecast=eq.false"],
            order: "obs_date.asc,cell_id.asc", maxPages: 80 }),
          sbFetch("wny_grid_climatology", { select: "cell_id,day_of_year,precip_mm,gdd_f,year_count",
            filters: [`cell_id=in.(${ids})`, `day_of_year=gte.${WXS.climoDoy(fetchFrom)}`, `day_of_year=lte.${WXS.climoDoy(fetchTo)}`],
            order: "cell_id.asc,day_of_year.asc", maxPages: 80 }),
          rmaP,
        ]).then(([rows, norms, rma]) => { if (alive) setWx({ key, cells, rows: rows || [], norms: norms || [], rma: rma || [] }); });
      })
      .catch((e) => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [cty && cty.key, year, bounds.to, fetchFrom, fetchTo]);

  const model = useMemo(() => {
    if (!wx || !cty || wx.key !== `${cty.key}|${year}|${fetchFrom}|${fetchTo}` || !bounds.to) return null;
    const all = WXS.countyDaily({ cellIds: wx.cells.map((c) => c.id), rows: wx.rows, norms: wx.norms, from: bounds.from, to: bounds.to });
    const days = WXS.trimTail(all);
    const plant = WXS.plantingDatesFor(cty.rows, wx.rma, RMAPW.resolveWindow, RMAPW.isFallSeededCrop);
    const years = Math.max(0, ...wx.norms.map((n) => Number(n.year_count) || 0));
    const freezeMed = WXS.medianFreezeDate(wx.cells, year);
    const jdMap = jdPlant && farm && jdPlant.key === `${farm.key}|${year}` ? jdPlant.map : null;
    const actual = WXS.actualPlanting(cty.rows, jdMap, year, RMAPW.isFallSeededCrop);
    return { days, summary: WXS.seasonSummary(days), months: WXS.monthTotals(days), plant, actual, years, cells: wx.cells,
             freeze: WXS.firstHardFreeze(days), freezeMed, events: WXS.claimDays(days, plant, freezeMed, actual) };
  }, [wx, cty && cty.key, year, bounds.from, bounds.to, jdPlant, farm && farm.key, fetchFrom, fetchTo]);

  useEffect(() => {
    const sp = model ? model.plant.spring : [];
    if (!sp.length) { if (cropCode) setCropCode(""); return; }
    if (!sp.some((c) => c.code === cropCode)) setCropCode(sp[0].code);
  }, [model, cropCode]);
  const crop = model ? model.plant.spring.find((c) => c.code === cropCode) || null : null;
  const win = crop ? crop.windows[0] : null;
  const winTotal = model && win ? WXS.windowTotal(model.days, win.earliest_planting_date || bounds.from, win.final_planting_date) : null;
  const winOpen = win && model && model.summary.to && model.summary.to < win.final_planting_date;

  // The range the Season view reads, and the choices for its start.
  const plantedFirst = model && crop ? ((model.actual.crops.find((c) => c.code === crop.code) || { dates: [] }).dates[0] || {}).date || null : null;
  const starts = [
    { k: "season", label: "Apr 1", from: bounds.from },
    plantedFirst && plantedFirst >= bounds.from ? { k: "planted", label: `Planted ${wxsMD(plantedFirst)}`, from: plantedFirst,
      title: `${wxsCrop(crop.crop)}${"’"}s first field planted, from the acreage report or John Deere` } : null,
    win && win.earliest_planting_date && win.earliest_planting_date > bounds.from ? { k: "rma", label: `RMA earliest ${wxsMD(win.earliest_planting_date)}`,
      from: win.earliest_planting_date, title: `RMA${"’"}s earliest planting date for ${wxsCrop(crop.crop).toLowerCase()}` } : null,
    { k: "custom", label: "Custom" },
  ].filter(Boolean);
  const pick = starts.find((x) => x.k === rng.mode) || starts[0];
  const vFrom = pick.k === "custom" ? (custom ? custom.from : bounds.from) : pick.from;
  const vTo = pick.k === "custom" ? (custom ? custom.to : bounds.to) : bounds.to;
  const vw = useMemo(() => {
    if (!model || !wx || !vFrom || !vTo) return null;
    const days = WXS.trimTail(WXS.countyDaily({ cellIds: wx.cells.map((c) => c.id), rows: wx.rows, norms: wx.norms, from: vFrom, to: vTo }));
    return { days, summary: WXS.seasonSummary(days), months: WXS.monthTotals(days) };
  }, [model, wx, vFrom, vTo]);

  // The season before on the same dates, read only when asked for.
  const pFrom = vFrom ? WXS.priorYear(vFrom) : null, pTo = vTo ? WXS.priorYear(vTo) : null;
  const [prior, setPrior] = useState(null);       // { key, days }
  const priorKey = useRef(null);                  // the key last asked for, so a re-render doesn't refetch
  useEffect(() => {
    let alive = true;
    if (!cmp || !wx || !wx.cells.length || !pFrom || !pTo) { priorKey.current = null; setPrior(null); return undefined; }
    const key = `${wx.key}|${pFrom}|${pTo}`;
    if (priorKey.current === key) return undefined;
    priorKey.current = key;
    const ids = wx.cells.map((c) => c.id).join(",");
    sbFetch("wny_grid_weather_daily", { select: "cell_id,obs_date,tmax_c,tmin_c,precip_mm,precip_gridmet_mm,source,is_forecast",
      filters: [`cell_id=in.(${ids})`, `obs_date=gte.${pFrom}`, `obs_date=lte.${pTo}`, "is_forecast=eq.false"],
      order: "obs_date.asc,cell_id.asc", maxPages: 80 })
      .then((rows) => {
        if (!alive) return;
        const days = WXS.countyDaily({ cellIds: wx.cells.map((c) => c.id), rows: rows || [], norms: wx.norms, from: pFrom, to: pTo });
        setPrior({ key, days });
      })
      .catch(() => { if (alive) setPrior({ key, days: [] }); });
    return () => { alive = false; if (priorKey.current === key) priorKey.current = null; };
  }, [cmp, wx, pFrom, pTo]);
  const priorDays = cmp && prior && wx && prior.key === `${wx.key}|${pFrom}|${pTo}` ? prior.days : null;
  // The prior season at the same point as this one's last counted day.
  const priorAt = (() => {
    if (!priorDays || !vw || !vw.summary.to) return null;
    const at = WXS.priorYear(vw.summary.to);
    const hit = priorDays.filter((d) => d.date <= at && d.counted).slice(-1)[0];
    return hit ? { rain_in: hit.rain_cum_in, pct: hit.normal_cum_in > 0 ? Math.round((hit.rain_cum_in / hit.normal_cum_in) * 100) : null } : null;
  })();

  const years = [];
  for (let y = CURRENT_CROP_YEAR; y >= 2015; y--) years.push(y);

  const picker = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
      {farmList.length > 1 && (
        <select value={farmKey || ""} onChange={(e) => { setFarmKey(e.target.value); setCounty(""); setWx(null); }}
          style={{ fontSize: 13, padding: "8px 10px", borderRadius: 10, border: `1px solid ${T.lineStrong}`, background: "#fff", color: T.ink, fontFamily: "inherit", maxWidth: "100%" }}>
          <option value="">{isPortal ? "Choose a farm…" : "Choose a farm…"}</option>
          {farmList.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
        </select>
      )}
      {view !== "next" && <select value={year} onChange={(e) => { setYear(Number(e.target.value)); setWx(null); }}
        style={{ fontSize: 13, padding: "8px 10px", borderRadius: 10, border: `1px solid ${T.lineStrong}`, background: "#fff", color: T.ink, fontFamily: "inherit" }}>
        {years.map((y) => <option key={y} value={y}>{y} season</option>)}
      </select>}
    </div>
  );

  if (!farmList.length) return <div style={{ ...uiCard(), fontSize: 13, color: T.body }}>No farm is linked to this login yet.</div>;
  if (!farm) return (
    <div>
      {picker}
      <div style={{ ...uiCard(), fontSize: 13, color: T.body, lineHeight: 1.6 }}>
        <b style={{ color: T.ink }}>Pick a farm to see its season.</b> The page opens on the counties the farm insures, with each crop{"’"}s RMA planting dates on the chart.
      </div>
    </div>
  );

  return (
    <div>
      {picker}
      {fields == null && <div style={{ ...uiCard(), color: T.faint, fontSize: 13 }}><UiSpinner />Reading {farm.name}{"’"}s insured fields…</div>}
      {fields != null && !counties.length && (
        <div style={{ ...uiCard(), fontSize: 13, color: T.body }}>No insured fields with a county are on file for {farm.name} in {year}.</div>
      )}
      {fields != null && counties.length > 0 && !inGrid.length && (
        <div style={{ ...uiCard(), fontSize: 13, color: T.body, lineHeight: 1.6 }}>
          {farm.name} insures in {outGrid.map((c) => c.name).join(", ")}. Season rain against normal covers New York and Pennsylvania counties for now; radar rain for these fields is under Radar and alerts.
        </div>
      )}
      {inGrid.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {inGrid.map((c) => (
            <button key={c.key} onClick={() => setCounty(c.key)} style={wxsChip(c.key === county)}>
              {c.name}{c.acres > 0 && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 600, opacity: c.key === county ? 0.75 : 0.6 }}>{Math.round(c.acres).toLocaleString()} ac</span>}
            </button>
          ))}
        </div>
      )}
      {cty && view === "next" && <WxsNextWeek cty={cty} isMobile={isMobile} />}
      {view !== "next" && err && <div style={{ ...uiCard(), color: T.danger, fontSize: 13, marginBottom: 12 }}>Couldn{"’"}t load the season{"’"}s weather: {err}</div>}
      {view !== "next" && cty && !model && !err && <div style={{ ...uiCard(), color: T.faint, fontSize: 13 }}><UiSpinner />Reading {cty.name} County{"’"}s weather for {year}…</div>}
      {view !== "next" && cty && model && !model.days.length && (
        <div style={{ ...uiCard(), fontSize: 13, color: T.body }}>No gridMET readings are on file for {cty.name} County in {year} yet.</div>
      )}
      {cty && model && model.days.length > 0 && view === "claims" && (
        <WxsClaimDays model={model} cty={cty} farm={farm} year={year} onOpenReport={onOpenReport} isPortal={isPortal} />
      )}
      {cty && model && model.days.length > 0 && view === "season" && vw && (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
            <span style={{ ...uiLabel, marginRight: 2 }}>Starting</span>
            <div role="tablist" aria-label="Start date" style={{ display: "flex", gap: 3, flexWrap: "wrap", background: T.surfaceAlt, border: `1px solid ${T.line}`, borderRadius: 12, padding: 4 }}>
              {starts.map((o) => (
                <button key={o.k} role="tab" aria-selected={pick.k === o.k} title={o.title}
                  onClick={() => setRng(o.k === "custom" ? { mode: "custom", from: rng.from || vFrom, to: rng.to || vTo } : { mode: o.k, from: "", to: "" })}
                  style={{ border: "none", borderRadius: 9, padding: isMobile ? "0 11px" : "6px 11px", minHeight: isMobile ? 38 : 30, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                           background: pick.k === o.k ? T.surface : "transparent", color: pick.k === o.k ? T.ink : T.faint, boxShadow: pick.k === o.k ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>{o.label}</button>
              ))}
            </div>
            {pick.k === "custom" && (
              <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {[["from", "From"], ["to", "To"]].map(([k, l]) => (
                  <label key={k} style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 12, color: T.muted }}>{l}
                    <input type="date" value={(custom && custom[k]) || ""} min={`${year}-01-01`} max={today < `${year}-12-31` ? today : `${year}-12-31`}
                      onChange={(e) => setRng((r) => ({ ...r, mode: "custom", [k]: e.target.value }))}
                      style={{ fontSize: 13, padding: "6px 8px", borderRadius: 9, border: `1px solid ${T.lineStrong}`, background: "#fff", color: T.ink, fontFamily: "inherit", minHeight: isMobile ? 38 : undefined }} />
                  </label>
                ))}
              </span>
            )}
            <button onClick={() => setCmp((v) => !v)} aria-pressed={cmp} style={{ ...wxsChip(cmp), padding: "6px 11px", fontSize: 12, minHeight: isMobile ? 38 : undefined }}>Compare {year - 1}</button>
          </div>
          {(() => {
            // The hero: the season's rain against normal, then the four facts a farmer
            // and an adjuster both ask next. Months that are still running are left out
            // of "wettest" and "driest", so a half-month never wins either.
            const sm = vw.summary;
            const full = (vw.months || []).filter((m) => m.pct != null && !m.partial);
            const wet = full.length ? full.reduce((a, b) => (b.pct > a.pct ? b : a)) : null;
            const dry = full.length ? full.reduce((a, b) => (b.pct < a.pct ? b : a)) : null;
            const mn = (m) => WXS_MONTHS[+m.month.slice(5, 7) - 1];
            const last = (vw.months || []).filter((m) => m.pct != null).slice(-1)[0] || null;
            const line = sm.normal_in == null ? null : [`Normal for the same days ${sm.normal_in.toFixed(1)} in.`,
              wet && dry && wet !== dry ? `Wettest month ${mn(wet)} (${wet.pct}%), driest ${mn(dry)} (${dry.pct}%).` : null].filter(Boolean).join(" ");
            return (
              <WxHero isMobile={isMobile}
                eyebrow={`Rain · ${cty.name} County · ${wxsMD(sm.from)} to ${wxsMD(sm.to)}`}
                value={sm.rain_in.toFixed(1)} unit="in"
                pill={sm.pct == null ? null : `${sm.pct}% of normal`} pillTone={wxvPctTone(sm.pct)}
                line={sm.pct == null ? null : line}
                stats={[
                  win && winTotal && winTotal.rain_in != null && winTotal.pct != null
                    ? { v: `${winTotal.pct}%`, l: `${wxsCrop(crop.crop)} planting window rain vs normal${winOpen ? ", so far" : ""}`, tone: WXV_TONE[wxvPctTone(winTotal.pct)][0] } : null,
                  last ? { v: `${last.pct}%`, l: `${mn(last)} rain vs normal${last.partial ? ", so far" : ""}`, tone: WXV_TONE[wxvPctTone(last.pct)][0] } : null,
                  sm.gdd != null ? { v: `${sm.gdd.toLocaleString()} GDD`, l: sm.gdd_pct == null ? `base 50°F since ${wxsMD(sm.from)}` : `${sm.gdd_pct >= 0 ? "+" : ""}${sm.gdd_pct}% vs normal, base 50°F` } : null,
                  cmp ? { v: priorAt ? `${priorAt.rain_in.toFixed(1)} in` : priorDays ? "No data" : "…", l: priorAt && priorAt.pct != null ? `${year - 1}, same dates (${priorAt.pct}% of normal)` : `${year - 1}, same dates`, tone: "#7a6a9a" }
                    : { v: model.freeze ? wxsMD(model.freeze.date) : "Not yet", l: `Hard freeze (28°F)${model.freezeMed ? ` · median ${wxsMD(model.freezeMed)}` : ""}`, tone: model.freeze ? WXV_TONE.frost[0] : null },
                ]} />
            );
          })()}

          <div style={{ ...uiCard(), marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, fontFamily: T.serif }}>Rain since {wxsMD(vw.summary.from)}, against normal</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                  {cty.name} County, the average of its {wx.cells.length} gridMET cells.{win ? ` ${wxsCrop(crop.crop)}${"’"}s insurance dates are drawn on.` : ""} Tap the chart for any day.
                </div>
              </div>
              {model.plant.spring.length > 1 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {model.plant.spring.map((c) => (
                    <button key={c.code} onClick={() => setCropCode(c.code)} style={{ ...wxsChip(c.code === cropCode), padding: "5px 11px", fontSize: 12 }}>{wxsCrop(c.crop)}</button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: T.body, margin: "8px 0 4px" }}>
              <span><span style={{ display: "inline-block", width: 16, height: 3, background: WXV_TONE.rain[0], verticalAlign: "middle", marginRight: 5 }} />{year}</span>
              <span><span style={{ display: "inline-block", width: 16, borderTop: "2px dashed #8a978a", verticalAlign: "middle", marginRight: 5 }} />Normal{model.years ? ` (${model.years}-year gridMET)` : ""}</span>
              {priorDays && priorDays.length > 0 && <span><span style={{ display: "inline-block", width: 16, height: 2, background: WXS_PRIOR, verticalAlign: "middle", marginRight: 5 }} />{year - 1}</span>}
              {win && <span><span style={{ display: "inline-block", width: 12, height: 10, background: WXS_BAND.plant, border: "1px solid #b9d3b9", verticalAlign: "middle", marginRight: 5 }} />Planting window</span>}
              {win && win.end_of_late_planting_date && <span><span style={{ display: "inline-block", width: 12, height: 10, background: WXS_BAND.late, border: "1px solid #e2c99a", verticalAlign: "middle", marginRight: 5 }} />Late planting</span>}
            </div>
            <WxsChart days={vw.days} win={win} isMobile={isMobile} prior={priorDays} priorYearLabel={year - 1} />
            {win && winTotal && winTotal.rain_in != null && (
              <div style={{ fontSize: 12.5, color: T.body, marginTop: 10, lineHeight: 1.55 }}>
                {wxsMD(win.earliest_planting_date || bounds.from)} to {wxsMD(win.final_planting_date)} ({wxsCrop(crop.crop).toLowerCase()}{"’"}s planting window
                {win.earliest_planting_date ? "" : " from Apr 1; RMA sets no earliest date here"}) took <b>{winTotal.rain_in.toFixed(2)} in</b>, against about {winTotal.normal_in.toFixed(2)} normal{winOpen ? ", so far" : ""}.
                {" "}Final planting {wxsMD(win.final_planting_date)}{win.end_of_late_planting_date ? `, late planting ends ${wxsMD(win.end_of_late_planting_date)}` : ""}.
                {crop.split && crop.windows.slice(1).map((w) => ` Type ${w.types.join(", ")}: final planting ${wxsMD(w.final_planting_date)}.`).join("")}
              </div>
            )}
          </div>

          <div style={{ ...uiCard(), marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, fontFamily: T.serif, marginBottom: 10 }}>Month by month</div>
            <WxsMonthBars months={vw.months} />
            <div style={{ fontSize: 11, color: T.faint, marginTop: 6 }}>
              Percent of normal for each month{(vw.months || []).some((m) => m.partial) ? "; * part of the month" : ""}.
            </div>
          </div>

          <WxNote>
            Rain is gridMET on both sides: the season and its normal come from the same instrument, so radar days never set against a gridMET normal.
            {vw.summary.missing > 0 && ` ${vw.summary.gaps.map(wxsMD).join(", ")} ${vw.summary.missing === 1 ? "has" : "have"} no county reading and ${vw.summary.missing === 1 ? "is" : "are"} left out of both totals.`}
            {cmp && ` ${year - 1} is the same county${"’"}s gridMET rain on the same dates.`}
            {model.plant.fall.length > 0 && ` ${model.plant.fall.map((c) => wxsCrop(c.crop)).join(", ")} ${model.plant.fall.length === 1 ? "was" : "were"} planted last fall, so ${model.plant.fall.length === 1 ? "its" : "their"} planting dates aren${"’"}t on this chart.`}
            {model.plant.unresolved.length > 0 && ` RMA publishes no planting window here for ${model.plant.unresolved.map((c) => wxsCrop(c.crop)).join(", ")}.`}
            {outGrid.length > 0 && ` ${outGrid.map((c) => c.name).join(", ")} ${outGrid.length === 1 ? "is" : "are"} outside New York and Pennsylvania, which the season grid covers today.`}
          </WxNote>
        </>
      )}
    </div>
  );
}

// The words and colours for each kind of claim day, one table, so the list and its
// counts agree.
const WXS_EVENT = {
  wet_window:   { label: "Wet planting window", color: "#2f6fa8", icon: "rain", tone: "rain" },
  heavy_rain:   { label: "Heavy rain", color: "#2f6fa8", icon: "rain", tone: "rain" },
  frost:        { label: "Frost after planting", color: "#5b7fa6", icon: "frost", tone: "frost" },
  heat:         { label: "Heat in corn pollination", color: "#a8453c", icon: "heat", tone: "heat" },
  dry_run:      { label: "Dry stretch", color: "#c08a2c", icon: "dry", tone: "warn" },
  early_freeze: { label: "Early hard freeze", color: "#5b7fa6", icon: "frost", tone: "frost" },
  missing:      { label: "No county reading", color: "#9aa59a", icon: "warn", tone: "plain" },
};
function wxsEventText(e) {
  const span = e.from === e.to ? wxsMD(e.from) : `${wxsMD(e.from)} to ${wxsMD(e.to)}`;
  const deg = (v) => `${Math.round(v)}°F`;
  switch (e.kind) {
    case "wet_window": return { span, title: `Wet ${wxsCrop(e.crop).toLowerCase()} planting window${e.types ? ` (type ${e.types.join(", ")})` : ""}`,
      detail: `${e.rain_in.toFixed(2)} in against ${e.normal_in.toFixed(2)} normal (${e.pct}%), with rain on ${e.wet_days} days.` };
    case "heavy_rain": return { span, title: "Heavy rain",
      detail: e.n > 1 ? `${e.rain_in.toFixed(2)} in over ${e.n} days, the largest ${e.max_in.toFixed(2)} in.` : `${e.rain_in.toFixed(2)} in in one day.` };
    case "frost": {
      const c = (e.crops || []).map((x) => `${x.planted_ac.toLocaleString()} of ${x.acres.toLocaleString()} ac of ${wxsCrop(x.crop).toLowerCase()}`);
      return { span, title: "Frost after planting",
        detail: `Low of ${deg(e.min_f)}${e.n > 1 ? ` across ${e.n} nights` : ""}${c.length ? `, with ${c.join(" and ")} planted by then` : ""}.` };
    }
    case "heat": return { span, title: "Heat in corn pollination",
      detail: `${e.n} day${e.n === 1 ? "" : "s"} at 90°F or more, high of ${deg(e.max_f)}.` };
    case "dry_run": return { span, title: "Dry stretch",
      detail: `${e.n} days in a row under a tenth of an inch, ${e.rain_in.toFixed(2)} in all told.` };
    case "early_freeze": return { span, title: "Early hard freeze",
      detail: `Low of ${deg(e.min_f)}, ${e.days_early} days before the county${"’"}s median first 28°F day (${wxsMD(e.median)}).` };
    case "missing": return { span, title: "No county reading",
      detail: `${e.n} day${e.n === 1 ? "" : "s"} without enough gridMET cells reporting. ${e.n === 1 ? "It is" : "They are"} left out of every total, not counted as dry.` };
    default: return { span, title: e.kind, detail: "" };
  }
}

// Where the printable record should be read: the acre-weighted centre of the farm's
// own insured fields in the county, or the centre of the county's cells when no field
// carries a location.
function wxsReportPoint(cty, cells, farmName) {
  let w = 0, la = 0, lo = 0;
  for (const f of cty.rows) {
    const a = Number(f.latitude), b = Number(f.longitude), ac = Number(f.acres) || 1;
    if (!Number.isFinite(a) || !Number.isFinite(b) || !a || !b) continue;
    w += ac; la += a * ac; lo += b * ac;
  }
  if (w > 0) return { lat: +(la / w).toFixed(4), lon: +(lo / w).toFixed(4), label: `${farmName}, ${cty.name} County fields` };
  const cs = (cells || []).filter((c) => Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lon)));
  if (!cs.length) return null;
  return { lat: +(cs.reduce((x, c) => x + Number(c.lat), 0) / cs.length).toFixed(4),
           lon: +(cs.reduce((x, c) => x + Number(c.lon), 0) / cs.length).toFixed(4), label: `${cty.name} County` };
}

function WxsClaimDays({ model, cty, farm, year, onOpenReport, isPortal }) {
  const ev = model.events || [];
  const real = ev.filter((e) => e.kind !== "missing");
  const open = (e) => {
    const pt = wxsReportPoint(cty, model.cells, farm.name);
    if (!pt || !onOpenReport) return;
    try {
      sessionStorage.setItem("cloverag_wxrec", JSON.stringify({ lat: pt.lat, lon: pt.lon, label: pt.label, clu: "",
        from: e.from, to: e.to, freezeF: 28, gddBase: 50 }));
    } catch (err) { /* storage off: the report opens on its own defaults */ }
    onOpenReport();
  };
  return (
    <div>
      <div style={{ ...uiCard(), marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, fontFamily: T.serif }}>Days that matter for a claim, {year}</div>
          {real.length > 0 && <WxPill tone="warn">{real.length} found</WxPill>}
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginTop: 3, lineHeight: 1.5 }}>
          From {cty.name} County{"’"}s weather and {isPortal ? "your" : "the farm’s"} RMA dates.
          {onOpenReport ? " Each opens the printable weather record." : " Your agent can print a weather record for any of these."}
        </div>
        {!ev.length && <div style={{ fontSize: 13, color: T.body, marginTop: 12 }}>Nothing stood out this season: no wet planting window, inch-plus day, frost after planting, pollination heat, two-week dry stretch or early freeze.</div>}
        {ev.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {ev.map((e, i) => {
              const t = wxsEventText(e);
              const k = WXS_EVENT[e.kind] || { color: T.faint };
              return (
                <div key={`${e.kind}-${e.from}-${i}`} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0",
                                    borderTop: i ? `1px solid ${T.lineSoft}` : "none" }}>
                  <WxIcon kind={k.icon} tone={k.tone} />
                  <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: T.ink }}>{t.title} <span style={{ fontWeight: 600, color: T.muted, ...uiNum }}>· {t.span}</span></div>
                    <div style={{ fontSize: 12.5, color: T.body, marginTop: 2, lineHeight: 1.5, ...uiNum }}>{t.detail}</div>
                    {onOpenReport && e.kind !== "missing" && (
                      <button onClick={() => open(e)} style={{ font: "inherit", fontSize: 12, fontWeight: 700, color: T.green, background: "none", border: 0, padding: "4px 0 0", cursor: "pointer" }}>Open weather record ›</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <WxNote label="How events are found">
        Every reading is the county average of its {model.cells.length} gridMET cells, so one field can see more or less than this.
        Thresholds: a planting window at {WXS.CLAIM.WET_WINDOW_PCT}% of normal or more, a day of {WXS.CLAIM.HEAVY_IN.toFixed(2)} in or more,
        a low of {WXS.CLAIM.FROST_F}°F or below through June once the first field was planted, {WXS.CLAIM.HEAT_F}°F or more from Jul 10 to Aug 15 where corn is insured,
        {" "}{WXS.CLAIM.DRY_RUN_DAYS} days in a row under {WXS.CLAIM.DRY_DAY_IN.toFixed(2)} in from May through September, and a hard freeze {WXS.CLAIM.EARLY_FREEZE_DAYS}+ days before the county{"’"}s median.
        {model.actual && (
          <div style={{ marginTop: 6 }}>
            Planting dates come from the acreage report{isPortal ? "" : ", or John Deere’s planting pass where the report has none"}.
            {" "}{Math.round(model.actual.dated_ac).toLocaleString()} of {Math.round(model.actual.acres).toLocaleString()} insured spring acres here have one
            {model.actual.first ? `, the first ${wxsMD(model.actual.first)}.` : ", so no frost is listed as after planting."}
          </div>
        )}
      </WxNote>
    </div>
  );
}

// The PRF tab: each insured interval's rainfall index against its trigger, for the
// farm's PRF coverage in force. wxPrf.js holds the rules; this only fetches and draws.
// No dollars anywhere on it: no liability, premium or indemnity, estimated or final.
const WXP_STATE = {
  final:    { label: "RMA final",       color: "#2d4a2d" },
  estimate: { label: "Estimate at close", color: "#7a5a00" },
  sofar:    { label: "Interval open",   color: "#5b6f8a" },
  upcoming: { label: "Not started",     color: "#9aa59a" },
  nodata:   { label: "Not published yet", color: "#9aa59a" },
  unknown:  { label: "Unknown interval", color: "#9aa59a" },
};

function WxPrfTab({ farmers = [], isMobile = false, isPortal = false, farmKey, setFarmKey }) {
  const prfFarms = useMemo(() => {
    const seen = new Map();
    for (const f of farmers || []) {
      if (!f || !f.id || !Array.isArray(f.prfPolicies) || !f.prfPolicies.length) continue;
      const k = String(f.id);
      if (!seen.has(k)) seen.set(k, { key: k, name: f.name || "Farm", prf: f.prfPolicies });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [farmers]);
  const farm = prfFarms.find((f) => f.key === farmKey) || (prfFarms.length === 1 ? prfFarms[0] : null);
  const thisYear = prfCoverageYear();
  const [year, setYear] = useState(thisYear);
  // The years a farm can be read in: this year, plus each settled year it holds.
  // A forward year is a rolled-over shell with nothing elected, so it is not offered.
  const farmYears = useMemo(() => {
    if (!farm) return [thisYear];
    const st = prfCoverageState(farm.prf, thisYear);
    return [...new Set([thisYear, ...st.priorYears])].sort((a, b) => b - a);
  }, [farm && farm.key, thisYear]);
  const farmYearsKey = farmYears.join("|");
  useEffect(() => { if (!farmYears.includes(year)) setYear(farmYears[0]); }, [farmYearsKey, year]);
  const scope = useMemo(() => (farm ? prfCoverageState(farm.prf, year) : null), [farm && farm.key, year]);
  const policies = scope ? scope.policies : [];
  const grids = useMemo(() => {
    const s = new Set();
    for (const p of policies) for (const iv of p.intervals || []) { const g = iv.gridId != null ? iv.gridId : iv.grid_id; if (g != null && g !== "") s.add(String(g)); }
    return [...s].sort();
  }, [policies]);
  const [ix, setIx] = useState(null);        // { key, rows }
  const [err, setErr] = useState("");
  const ixKey = `${grids.join(",")}|${year}`;
  useEffect(() => {
    let alive = true;
    setErr("");
    if (!grids.length) { setIx({ key: ixKey, rows: [] }); return undefined; }
    setIx(null);
    sbFetch("prf_grid_indices", {
      select: "grid_id,interval_code,final_index_value,final_rainfall_inches,is_final,estimated_index_value,estimated_rainfall_inches,normal_rainfall_inches,normal_to_date_inches,estimated_as_of",
      filters: [`grid_id=in.(${grids.join(",")})`, `crop_year=eq.${year}`] })
      .then((rows) => { if (alive) setIx({ key: ixKey, rows: rows || [] }); })
      .catch((e) => { if (alive) { setIx({ key: ixKey, rows: [] }); setErr(String((e && e.message) || e)); } });
    return () => { alive = false; };
  }, [ixKey, year]);
  const plan = useMemo(() => {
    if (!ix || ix.key !== ixKey) return null;
    return WXP.prfPlan({ policies, indexRows: ix.rows, year, nowMs: Date.now(), parseCode: parsePRFCode, finalInches: prfFinalInches });
  }, [ix, ixKey, policies, year]);

  const sel = { fontSize: 13, padding: "8px 10px", borderRadius: 10, border: `1px solid ${T.lineStrong}`, background: "#fff", color: T.ink, fontFamily: "inherit", maxWidth: "100%" };
  const picker = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
      {prfFarms.length > 1 && (
        <select value={farm ? farm.key : ""} onChange={(e) => setFarmKey(e.target.value)} style={sel}>
          <option value="">Choose a farm with PRF…</option>
          {prfFarms.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
        </select>
      )}
      {farm && farmYears.length > 1 && (
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={sel}>
          {farmYears.map((y) => <option key={y} value={y}>{y} crop year</option>)}
        </select>
      )}
    </div>
  );

  if (!prfFarms.length) return <div style={{ ...uiCard(), fontSize: 13, color: T.body }}>{isPortal ? "You don’t have PRF coverage on file." : "No farm here has PRF coverage on file."}</div>;
  if (!farm) return (
    <div>
      {picker}
      <div style={{ ...uiCard(), fontSize: 13, color: T.body, lineHeight: 1.6 }}>
        <b style={{ color: T.ink }}>Pick a farm to see its PRF intervals.</b> Each interval the farm insures reads its grid{"’"}s rainfall index against the trigger.
      </div>
    </div>
  );
  if (!policies.length) return (
    <div>
      {picker}
      <div style={{ ...uiCard(), fontSize: 13, color: T.body, lineHeight: 1.6 }}>
        {farm.name} has no PRF coverage for {year}.{scope && scope.priorYears.length ? ` Its last PRF policy was ${scope.priorYears[0]}, which you can pick above.` : ""}
      </div>
    </div>
  );
  if (!plan) return <div>{picker}<div style={{ ...uiCard(), fontSize: 13, color: T.muted }}>{err ? `Couldn’t load the rainfall index: ${err}` : "Loading the rainfall index…"}</div></div>;

  const allIv = plan.flatMap((g) => g.intervals);
  const below = allIv.filter((i) => i.below === true).length;
  const judged = allIv.filter((i) => i.below != null).length;
  const pending = allIv.filter((i) => i.state === "upcoming" || i.state === "sofar").length;
  return (
    <div>
      {picker}
      {(() => {
        // Closest to its trigger among intervals with an index (final or estimate at
        // close): the one number a PRF grower asks about first.
        const withIx = plan.flatMap((g) => g.intervals.filter((iv) => iv.index != null && g.trigger != null).map((iv) => ({ iv, g })));
        const close = withIx.length ? withIx.reduce((a, b) => (b.iv.index - b.g.trigger < a.iv.index - a.g.trigger ? b : a)) : null;
        return (
          <WxHero isMobile={isMobile}
            eyebrow={`PRF ${year} · ${farm.name}`}
            value={judged ? `${below} of ${judged}` : allIv.length} unit={judged ? "closed below trigger" : `interval${allIv.length === 1 ? "" : "s"} insured`}
            pill={judged ? (below ? `${below} below trigger` : "None below trigger") : "None closed yet"}
            pillTone={judged ? (below ? "weak" : "good") : "plain"}
            line={close ? `Closest: ${close.iv.label}, grid ${close.g.grid}, index ${Math.round(close.iv.index)} against a ${close.g.trigger} trigger${close.iv.state === "final" ? "" : " (estimate)"}.` : null}
            stats={[
              { v: allIv.length, l: `interval${allIv.length === 1 ? "" : "s"} on ${plan.length} grid${plan.length === 1 ? "" : "s"}` },
              { v: pending, l: "not started or still open" },
            ]} />
        );
      })()}
      {err && <div style={{ ...uiCard(), fontSize: 12.5, color: "#a8453c", marginBottom: 12 }}>Couldn{"’"}t load the rainfall index: {err}</div>}
      {plan.map((g) => (
        <div key={`${g.grid}|${g.county}|${g.trigger}`} style={{ ...uiCard(), marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, fontFamily: T.serif }}>Grid {g.grid}{g.county ? ` · ${wxsTitle(g.county)} County` : ""}</div>
            <div style={{ fontSize: 12, color: T.muted, ...uiNum }}>{g.trigger != null ? `${g.trigger}% coverage · pays below ${g.trigger}% of normal` : "coverage level not on file"}</div>
          </div>
          <div style={{ marginTop: 8 }}>
            {g.intervals.map((iv, i) => {
              const st = WXP_STATE[iv.state] || WXP_STATE.nodata;
              const verdict = iv.below === true ? { t: "Below trigger", c: WXV_TONE.weak[0] } : iv.below === false ? { t: "Above trigger", c: WXV_TONE.good[0] } : null;
              // One track per interval, 0 to 220: the index (solid when RMA final, paler
              // as an estimate), or for an open interval its rain so far as a percent of
              // normal so far, with no trigger line because it isn't judged yet.
              const MAX = 220;
              const pos = (v) => `${Math.max(0, Math.min(100, (v / MAX) * 100))}%`;
              const val = iv.index != null ? iv.index : iv.state === "sofar" ? iv.toDate : null;
              const fill = iv.index != null ? (iv.index < 100 ? WXV_TONE.warn[0] : WXV_TONE.rain[0]) : "#9fb3c8";
              // The index is the interval's rain as a percent of normal; checked here
              // against its own inches, and an agent sees any row where the two part.
              const chk = iv.index != null ? WXP.pctOfNormal(iv) : { pct: null, agrees: null };
              return (
                <div key={iv.code} style={{ display: "grid", gap: 3, padding: "8px 0", borderTop: i ? `1px solid ${T.lineSoft}` : "none" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "64px minmax(0,1fr) 54px", gap: 8, alignItems: "center", fontSize: 12 }}>
                    <span style={{ fontWeight: 800, color: T.ink }}>{iv.label}</span>
                    <span style={{ position: "relative", height: 16, background: T.surfaceAlt, borderRadius: 5 }}>
                      {val != null && <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: pos(val), borderRadius: 5, background: fill, opacity: iv.state === "final" ? 1 : 0.55 }} />}
                      {iv.index != null && g.trigger != null && <span title={`Trigger: ${g.trigger}% of normal`} style={{ position: "absolute", left: pos(g.trigger), top: -3, bottom: -3, width: 2, background: WXV_TONE.weak[0] }} />}
                      <span title="Normal (100%)" style={{ position: "absolute", left: pos(100), top: 0, bottom: 0, width: 1, background: T.faint, opacity: 0.6 }} />
                    </span>
                    <span style={{ fontWeight: 800, color: T.ink, textAlign: "right", fontSize: 13.5, ...uiNum }}>{val != null ? `${Math.round(val)}%` : "—"}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: T.faint, paddingLeft: 72, lineHeight: 1.45, ...uiNum }}>
                    <span style={{ fontWeight: 800, color: verdict ? verdict.c : st.color }}>{verdict ? verdict.t
                      : iv.state === "sofar" && WXP.elapsed(iv.code, year, Date.now()) === 1 ? "Closed, rain to date" : st.label}</span>
                    {iv.index != null
                      ? <>{` · ${Math.round(iv.index)}% of normal`}{iv.inches != null ? `, ${iv.inches.toFixed(2)} in` : ""}{iv.normal != null ? ` of ${iv.normal.toFixed(2)} in` : ""}{` · index ${Math.round(iv.index)}`}{iv.state !== "final" && iv.asOf ? ` · as of ${wxsMD(String(iv.asOf).slice(0, 10))}` : ""}
                          {!isPortal && chk.agrees === false && <span style={{ color: WXV_TONE.warn[0], fontWeight: 800 }}>{` · check: its inches give ${Math.round(chk.pct)}%`}</span>}</>
                      : iv.state === "sofar" && iv.toDate != null
                      ? <>{` · ${Math.round(iv.toDate)}% of normal so far`}{iv.inches != null ? `, ${iv.inches.toFixed(2)} in` : ""}{iv.normalToDate != null ? ` of ${iv.normalToDate.toFixed(2)} in` : ""}{iv.asOf ? ` · as of ${wxsMD(String(iv.asOf).slice(0, 10))}` : ""}</>
                      : null}
                    {iv.acres ? ` · ${Math.round(iv.acres * 10) / 10} ac` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ fontSize: 11.5, color: T.faint, marginBottom: 6 }}>
        Each bar is the interval{"’"}s rain as a percent of normal. Red line is the trigger, grey is normal (100%). Paler bars are estimates or open intervals.
      </div>
      <WxNote>
        RMA{"’"}s rainfall index is the grid{"’"}s rain as a percent of its normal, so an index of 85 is 85% of normal. RMA final is the settled figure. Before that, Silas shows its own estimate, and compares it with the trigger only once the interval has closed. An open interval shows rain to date against normal to date.
        {isPortal ? " Payments are figured by your insurance company from RMA’s final index." : " Payments come from RMA’s final index only; see the farm’s PRF card for coverage."}
      </WxNote>
    </div>
  );
}

// Next 7 days: the county's forecast from the same 4-km grid, with what each day
// means for a farm. wxNext.js holds the rules; this only fetches and draws.
const WXN_FLAG = {
  dry:         { label: "Likely field day", color: "#2d6a2d", bg: "#e7f1e7" },
  heavy:       { label: "Heavy rain",       color: "#2f6fa8", bg: "#e6eff8" },
  freeze:      { label: "Frost",            color: "#5a5aa8", bg: "#ecebf7" },
  hard_freeze: { label: "Hard freeze",      color: "#3a3a7a", bg: "#e3e2f2" },
  heat:        { label: "Heat",             color: "#a8453c", bg: "#f7e8e6" },
};

// The day's picture, drawn from wxNext.dayIcon's kind.
function WxDayIcon({ kind, size = 30 }) {
  if (!kind) return <span style={{ width: size, height: size, display: "inline-block" }} />;
  const sun = "#e3a21a", cloud = "#9aa7b4", cloudDk = "#7d8b99", drop = WXV_TONE.rain[0];
  const Sun = ({ cx, cy, r }) => (
    <g>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => { const t = (a * Math.PI) / 180; return (
        <line key={a} x1={cx + Math.cos(t) * (r + 2.5)} y1={cy + Math.sin(t) * (r + 2.5)} x2={cx + Math.cos(t) * (r + 5.5)} y2={cy + Math.sin(t) * (r + 5.5)} stroke={sun} strokeWidth="2" strokeLinecap="round" />); })}
      <circle cx={cx} cy={cy} r={r} fill={sun} />
    </g>
  );
  const Cloud = ({ dx = 0, dy = 0, fill = cloud }) => (
    <path transform={`translate(${dx},${dy})`} fill={fill} d="M9 26h17a6 6 0 0 0 0-12 8 8 0 0 0-15.3-1.8A6.5 6.5 0 0 0 9 26z" />
  );
  const Drops = ({ n }) => (
    <g stroke={drop} strokeWidth="2.2" strokeLinecap="round">
      {(n === 3 ? [11, 18, 25] : [14, 22]).map((x) => <line key={x} x1={x} y1="29" x2={x - 2} y2="34" />)}
    </g>
  );
  const body = {
    sunny: <Sun cx={18} cy={18} r={7} />,
    partly: <g><Sun cx={13} cy={12} r={6} /><Cloud dx={3} dy={4} /></g>,
    cloudy: <g><Cloud dx={-2} dy={-2} fill={cloud} /><Cloud dx={3} dy={3} fill={cloudDk} /></g>,
    showers: <g><Sun cx={13} cy={10} r={5} /><Cloud dx={2} dy={0} /><Drops n={2} /></g>,
    rain: <g><Cloud dx={1} dy={-1} fill={cloudDk} /><Drops n={3} /></g>,
  }[kind];
  const label = { sunny: "Sunny", partly: "Partly cloudy", cloudy: "Cloudy", showers: "Showers", rain: "Rain" }[kind];
  return <svg width={size} height={size} viewBox="0 0 36 36" role="img" aria-label={label}><title>{label}</title>{body}</svg>;
}

function WxsNextWeek({ cty, isMobile }) {
  const [fc, setFc] = useState(null);     // { key, cells, rows }
  const [err, setErr] = useState("");
  const [hr, setHr] = useState(null);     // { key, days: Map(date -> fieldwork day) }
  const today = wxsTodayIso();
  const last = WXN.isoAdd(today, WXN.NEXT.DAYS - 1);
  const key = `${cty.key}|${today}`;
  useEffect(() => {
    let alive = true;
    setFc(null); setErr("");
    sbFetch("wny_grid_cells", { select: "id,lat,lon", filters: [`county_fips=eq.${cty.key}`] })
      .then((cells) => {
        if (!alive) return null;
        if (!cells || !cells.length) { setFc({ key, cells: [], rows: [] }); return null; }
        // Chance of rain, wind and sky cover for the county's centre, from the same
        // hourly forecast the Radar tab reads. Optional: the week stands without it.
        const ok = cells.filter((c) => Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lon)));
        if (ok.length) {
          const la = (ok.reduce((a, c) => a + Number(c.lat), 0) / ok.length).toFixed(3);
          const lo = (ok.reduce((a, c) => a + Number(c.lon), 0) / ok.length).toFixed(3);
          fetch(`/api/market?fn=precip&op=fieldwork&pts=${encodeURIComponent(`${la},${lo}`)}&days=7`)
            .then((r) => r.json())
            .then((w) => {
              if (!alive) return;
              const loc = w && w.ok && w.locations && w.locations[0];
              setHr({ key, days: new Map(((loc && loc.days) || []).map((d) => [d.date, d])) });
            })
            .catch(() => { if (alive) setHr({ key, days: new Map() }); });
        }
        return sbFetch("wny_grid_weather_daily", { select: "cell_id,obs_date,tmax_c,tmin_c,precip_mm,is_forecast,source,synced_at",
          filters: [`cell_id=in.(${cells.map((c) => c.id).join(",")})`, `obs_date=gte.${today}`, `obs_date=lte.${last}`, "is_forecast=eq.true"],
          order: "obs_date.asc,cell_id.asc", maxPages: 10 })
          .then((rows) => { if (alive) setFc({ key, cells, rows: rows || [] }); });
      })
      .catch((e) => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [key, cty.key, today, last]);
  const days = useMemo(() => (fc && fc.key === key
    ? WXN.forecastDays({ cellIds: fc.cells.map((c) => c.id), rows: fc.rows, todayIso: today }) : null), [fc, key, today]);
  const sum = days ? WXN.weekSummary(days) : null;
  const synced = fc && fc.rows.length ? fc.rows.reduce((m, r) => (r.synced_at && r.synced_at > m ? r.synced_at : m), "") : "";

  if (err) return <div style={{ ...uiCard(), color: T.danger, fontSize: 13 }}>Couldn{"’"}t load the forecast: {err}</div>;
  if (!days) return <div style={{ ...uiCard(), color: T.faint, fontSize: 13 }}><UiSpinner />Reading {cty.name} County{"’"}s forecast…</div>;
  if (!sum.counted) return <div style={{ ...uiCard(), fontSize: 13, color: T.body }}>No forecast is on file for {cty.name} County right now.</div>;
  const wk = (iso) => new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
  return (
    <div>
      <WxHero isMobile={isMobile}
        eyebrow={`Next 7 days · ${cty.name} County`}
        value={sum.rain_in.toFixed(2)} unit="in of rain"
        pill={`${sum.dry} likely field day${sum.dry === 1 ? "" : "s"}`} pillTone={sum.dry ? "good" : "plain"}
        line={sum.wettest && sum.wettest.rain_in >= 0.1 ? `Most on ${wk(sum.wettest.date)} ${wxsMD(sum.wettest.date)}, ${sum.wettest.rain_in.toFixed(2)} in.` : "No day over a tenth of an inch."}
        stats={[
          { v: `${sum.coldest.lo_f}°F`, l: `coldest night, ${wk(sum.coldest.date)} ${wxsMD(sum.coldest.date)}`, tone: sum.coldest.lo_f <= WXN.NEXT.HARD_F ? WXV_TONE.frost[0] : null },
          { v: `${sum.warmest.hi_f}°F`, l: `warmest day, ${wk(sum.warmest.date)} ${wxsMD(sum.warmest.date)}`, tone: sum.warmest.hi_f >= WXN.NEXT.HEAT_F ? WXV_TONE.heat[0] : null },
        ]} />
      {(() => {
        // One row a day: the picture, the low-to-high bar on the week's own scale,
        // the rain with its chance, the wind, and the day's flags. Field days are
        // the green ones.
        const hd = hr && hr.key === key ? hr.days : null;
        const c = days.filter((d) => d.counted);
        const tMin = Math.min(...c.map((d) => d.lo_f)), tMax = Math.max(...c.map((d) => d.hi_f));
        const lo = Math.floor((tMin - 2) / 5) * 5, hi = Math.ceil((tMax + 2) / 5) * 5;
        const pos = (t) => `${((t - lo) / Math.max(1, hi - lo)) * 100}%`;
        const rMax = Math.max(0.5, ...c.map((d) => d.rain_in));
        const tc = (t) => (t <= WXN.NEXT.HARD_F ? "#3a3a9a" : t <= WXN.NEXT.FROST_F ? "#3b7fa8" : t >= WXN.NEXT.HEAT_F ? WXV_TONE.heat[0] : t >= 75 ? "#d07a2a" : t >= 55 ? "#c9a13a" : "#6f9fbf");
        const cols = isMobile ? "52px 30px minmax(0,1fr) 62px" : "86px 34px minmax(0,1fr) 120px 92px 118px";
        return (
          <div style={{ ...uiCard(), marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, fontFamily: T.serif }}>The week ahead</div>
              <div style={{ fontSize: 11.5, color: T.faint }}>A forecast, so it moves.</div>
            </div>
            {!isMobile && (
              <div style={{ display: "grid", gridTemplateColumns: cols, gap: 10, fontSize: 10.5, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: ".05em", margin: "12px 0 2px", padding: "0 8px" }}>
                <span>Day</span><span /><span>Low to high</span><span>Rain · chance</span><span>Wind</span><span />
              </div>
            )}
            <div style={{ display: "grid", gap: 4, marginTop: isMobile ? 10 : 0 }}>
              {days.map((d) => {
                const h = hd ? hd.get(d.date) : null;
                const field = d.counted && d.flags.includes("dry");
                const other = d.counted ? d.flags.filter((f) => f !== "dry").map((f) => WXN_FLAG[f]).filter(Boolean) : [];
                const icon = d.counted ? WXN.dayIcon({ rain_in: d.rain_in, popPct: h ? h.popPct : null, cloudPct: h ? h.cloudPct : null }) : null;
                const wind = h && h.maxWindMph != null ? `${h.maxWindMph} mph${h.maxGustMph > h.maxWindMph + 4 ? `, gusts ${h.maxGustMph}` : ""}` : null;
                const tags = (
                  <span style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "flex-end" }}>
                    {field && <span style={{ fontSize: 10.5, fontWeight: 800, color: WXV_TONE.good[0], background: WXV_TONE.good[1], borderRadius: 999, padding: "2px 8px" }}>Field day</span>}
                    {other.map((k) => <span key={k.label} style={{ fontSize: 10.5, fontWeight: 800, color: k.color, background: k.bg, borderRadius: 999, padding: "2px 8px" }}>{k.label}</span>)}
                  </span>
                );
                return (
                  <div key={d.date} style={{ display: "grid", gridTemplateColumns: cols, gap: isMobile ? 8 : 10, alignItems: "center", padding: isMobile ? "8px 6px" : "8px",
                                             borderRadius: 10, background: field ? "#f3f8f1" : "transparent", borderLeft: `3px solid ${field ? WXV_TONE.good[0] : "transparent"}` }}>
                    <span style={{ lineHeight: 1.15 }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: T.ink }}>{d.date === today ? "Today" : wk(d.date)}</span>
                      <span style={{ fontSize: 11, color: T.faint, ...uiNum }}>{wxsMD(d.date)}</span>
                    </span>
                    <WxDayIcon kind={icon} size={isMobile ? 28 : 32} />
                    {d.counted ? (
                      <span style={{ display: "grid", gap: 3, minWidth: 0 }}>
                        <span style={{ display: "grid", gridTemplateColumns: "30px minmax(0,1fr) 30px", gap: 6, alignItems: "center", fontSize: 12.5, fontWeight: 800, ...uiNum }}>
                          <span style={{ color: tc(d.lo_f), textAlign: "right" }}>{d.lo_f}°</span>
                          <span style={{ position: "relative", height: 8, background: T.surfaceAlt, borderRadius: 4 }}>
                            <span style={{ position: "absolute", left: pos(d.lo_f), right: `calc(100% - ${pos(d.hi_f)})`, top: 0, bottom: 0, borderRadius: 4,
                                           background: `linear-gradient(90deg, ${tc(d.lo_f)}, ${tc(d.hi_f)})` }} />
                            {lo < WXN.NEXT.FROST_F && hi > WXN.NEXT.FROST_F && <span title="32°F" style={{ position: "absolute", left: pos(WXN.NEXT.FROST_F), top: -3, bottom: -3, width: 1, background: "#3b7fa8", opacity: 0.5 }} />}
                          </span>
                          <span style={{ color: tc(d.hi_f) }}>{d.hi_f}°</span>
                        </span>
                        {isMobile && (wind || field || other.length > 0) && (
                          <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", fontSize: 11, color: T.faint }}>{wind && <span>Wind {wind}</span>}{tags}</span>
                        )}
                      </span>
                    ) : <span style={{ fontSize: 12, color: T.faint }}>No forecast for this day</span>}
                    <span style={{ display: "grid", gap: 3, ...uiNum }}>
                      {d.counted && (
                        <>
                          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {!isMobile && <span style={{ flex: "0 0 40px", height: 8, background: T.surfaceAlt, borderRadius: 4, position: "relative", overflow: "hidden" }}>
                              <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, (d.rain_in / rMax) * 100)}%`, background: WXV_TONE.rain[0], borderRadius: 4 }} />
                            </span>}
                            <span style={{ fontSize: 12.5, fontWeight: 800, color: d.rain_in >= 0.1 ? WXV_TONE.rain[0] : T.muted }}>{d.rain_in < 0.005 ? "0" : d.rain_in.toFixed(2)}"</span>
                          </span>
                          {h && h.popPct != null && <span style={{ fontSize: 11, color: h.popPct >= 50 ? WXV_TONE.rain[0] : T.faint, fontWeight: h.popPct >= 50 ? 700 : 400 }}>{h.popPct}% chance</span>}
                        </>
                      )}
                    </span>
                    {!isMobile && <span style={{ fontSize: 12, color: T.body, ...uiNum }}>{wind || <span style={{ color: T.faint }}>—</span>}</span>}
                    {!isMobile && tags}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      <WxNote>
        Rain amounts and temperatures are the county average of {fc.cells.length} grid cells, Open-Meteo ensemble on the 4-km grid{synced ? `, updated ${new Date(synced).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}.
        Chance of rain, wind and sky cover are the hourly GFS forecast for the county{"’"}s centre, so a day can show a chance of rain with little in the amount. Radar and the hourly view are under Radar and alerts.
        A likely field day is under {WXN.NEXT.DRY_IN.toFixed(2)} in after a day under {WXN.NEXT.DRY_PREV_IN.toFixed(2)} in. Heavy rain is {WXN.NEXT.HEAVY_IN.toFixed(2)} in or more, frost a low of {WXN.NEXT.FROST_F}°F or below, a hard freeze {WXN.NEXT.HARD_F}°F, heat {WXN.NEXT.HEAT_F}°F or above.
      </WxNote>
    </div>
  );
}

function WeatherHub({ farmers = [], isMobile = false, mode = null, preview = false, onNav = null, initialTab = null }) {
  const isPortal = mode === "portal";
  // The Claim report is the old Weather Records page: admin only, never in the portal,
  // and the server refuses its data to anyone else whatever this page shows.
  const canReport = !isPortal && isCurrentUserAdmin();
  const tabs = [
    { k: "season", label: "Season" },
    { k: "claims", label: "Events" },
    // PRF only where a farm on this page carries a PRF policy.
    ...((farmers || []).some((f) => f && Array.isArray(f.prfPolicies) && f.prfPolicies.length) ? [{ k: "prf", label: "PRF" }] : []),
    { k: "next", label: "Next 7 days" },
    { k: "radar", label: "Radar and alerts" },
    ...(canReport ? [{ k: "report", label: "Claim report" }] : []),
  ];
  const readTab = () => { try { return sessionStorage.getItem("cloverag_wx_tab"); } catch (e) { return null; } };
  const [tab, setTabState] = useState(() => {
    const want = initialTab || readTab() || "season";
    return tabs.some((t) => t.k === want) ? want : "season";
  });
  const setTab = (k) => { setTabState(k); try { sessionStorage.setItem("cloverag_wx_tab", k); } catch (e) { /* storage off */ } };
  useEffect(() => { if (initialTab && tabs.some((t) => t.k === initialTab)) setTabState(initialTab); }, [initialTab]);
  const shown = tabs.some((t) => t.k === tab) ? tab : "season";
  const readFarm = () => { try { return sessionStorage.getItem("cloverag_wx_farm") || ""; } catch (e) { return ""; } };
  const [farmKey, setFarmKeyState] = useState(readFarm);
  const setFarmKey = (k) => { setFarmKeyState(k); try { sessionStorage.setItem("cloverag_wx_farm", k); } catch (e) { /* storage off */ } };
  // Links inside the two older pages land on this page's own tabs now.
  const innerNav = (k) => {
    if (k === "wxrecords" && canReport) return setTab("report");
    if (k === "rainwatch") return setTab("season");
    if (onNav) onNav(k);
  };
  return (
    <div style={{ padding: isMobile ? 12 : 20, maxWidth: 1180, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ ...uiLabel, marginBottom: 2 }}>{isPortal ? "YOUR FARM'S WEATHER" : "WEATHER"}</div>
        <h1 style={{ ...uiPageTitle(isMobile) }}>Weather</h1>
        <div style={{ fontSize: 12.5, color: T.body, marginTop: 5, maxWidth: 800, lineHeight: 1.55 }}>
          The season against normal in {isPortal ? "your" : "the farm’s"} counties, with each crop{"’"}s insurance dates.
        </div>
      </div>
      <WxTabs tabs={tabs} value={shown} onChange={setTab} />
      {/* Season, Claim days and Next 7 days are one component, so switching keeps the farm and county. */}
      {(shown === "season" || shown === "claims" || shown === "next") && (
        <WxSeasonTab farmers={farmers} isMobile={isMobile} isPortal={isPortal} farmKey={farmKey} setFarmKey={setFarmKey}
          view={shown} onOpenReport={canReport ? () => setTab("report") : null} />
      )}
      {shown === "prf" && <WxPrfTab farmers={farmers} isMobile={isMobile} isPortal={isPortal} farmKey={farmKey} setFarmKey={setFarmKey} />}
      {shown === "radar" && <WeatherView isMobile={isMobile} mode={mode} preview={preview} onNav={innerNav} embedded />}
      {shown === "report" && canReport && <WeatherRecordsView farmers={farmers} isMobile={isMobile} onNav={innerNav} embedded />}
    </div>
  );
}

function WeatherView({ isMobile = false, mode = null, preview = false, onNav = null, embedded = false }) {
  const isPortal = mode === "portal";
  const [locs, setLocs] = useState(null);
  const [fc, setFc] = useState(null);
  const [gauge, setGauge] = useState(null);
  const [now, setNow] = useState(null);         // op=nowcast, or null when it can't say
  const [arch, setArch] = useState(null);       // archived forecast rows, by location id
  const [work, setWork] = useState(null);       // op=fieldwork
  const [skill, setSkill] = useState(null);     // op=skill — the verified track record
  // Wind is the second strip on the Hours tab. Collapsible because half the time
  // the question is only "when does it rain", and two strips per day doubles the
  // height you have to scan to answer it.
  const [showWind, setShowWind] = useState(true);
  // The hour tapped on the Forecast strip: { li, i } — scoped to the location so
  // switching farms clears it rather than pointing into another farm's array.
  const [pickHr, setPickHr] = useState(null);
  // Alerts the user has set up, and the row being composed.
  const [alerts, setAlerts] = useState(null);
  const [alertForm, setAlertForm] = useState(null);   // null = form closed
  // The NWS forecaster's own words for the office covering these farms.
  const [synopsis, setSynopsis] = useState(null);
  const [synOpen, setSynOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // radar | gauges | forecast | table. Radar leads: it is the only view that
  // answers "what is happening right now", it needs no saved locations to be
  // useful, and it is what the page is called Weather for.
  const [tab, setTab] = useState("radar");
  // Which accumulation the gauges lead with. ONE selector above every card, not
  // a control per card: the point of a watchlist is comparing locations, which
  // only works if they are all showing the same window.
  const [gWin, setGWin] = useState("today");
  // Forecast shows ONE location at a time. Side by side, each card was mostly
  // empty plot with cramped labels, and the detail worth having (chance, high,
  // low, partial-day caveats) had nowhere to go.
  const [fcLoc, setFcLoc] = useState(0);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", lat: "", lon: "" });
  // Typing latitude and longitude is not a way to save a farm. Two better
  // routes, both feeding the same fields: pick a customer (207 of 282 farmers
  // already carry coordinates), or drop a pin on a map.
  const [farmOpts, setFarmOpts] = useState(null);
  const [farmQuery, setFarmQuery] = useState("");
  const pickMapEl = useRef(null);
  const pickMapRef = useRef(null);
  const pickMarkRef = useRef(null);
  const [Lp, setLp] = useState(typeof window !== "undefined" ? window.L : null);

  // PREVIEW READS THE FARMER'S OWN LIST — corrected 2026-08-13.
  //
  // This whole screen used to blank itself in preview: no locations, no gauges, no
  // forecast, no alerts, and the four tabs hidden. That was RIGHT under the OLD
  // preview, which ran on the ADMIN'S token — `weather_locations` is scoped
  // `created_by = auth.uid()`, so the query returned KEVIN'S list under a banner
  // claiming to be the farmer's screen, and each row's `remove` was a live delete of
  // the admin's own saved location.
  //
  // Preview now mints the FARMER'S real session (see the PREVIEW-AS block at the top
  // of this file), so `auth.uid()` IS them and this query returns exactly what they
  // see — nothing to substitute and nothing to trim. Leaving the old guard in place
  // meant an agent looking at Weather in preview was shown a page with no way to save
  // a location and no gauge or forecast on it, and concluded the PORTAL was missing
  // the feature. It isn't: two portal users have saved locations and are being
  // scored daily.
  //
  // Writes stay live here, like every other preview write (CLAUDE.md, 2026-08-13) —
  // saving a rain gauge on the grower's account while on the phone with them is the
  // case that policy exists for.
  const loadLocs = useCallback(() => {
    sbFetch("weather_locations", { select: "id,name,lat,lon,sort_order", order: "sort_order.asc,created_at.asc,id.asc" })
      .then((rows) => setLocs(rows || []))
      .catch(() => { setLocs([]); setErr("Could not load saved locations."); });
  }, []);
  useEffect(() => { loadLocs(); }, [loadLocs]);

  // PORTAL ONLY, and only on the first-run empty state. policy_fields is
  // farmer-scoped by RLS, so a grower reads their own fields; under an AGENT'S
  // session the same query returns the whole book, which is why the guard is on
  // the fetch and not only on the render. The suggestions name counties, never
  // customers.
  const [mySpots, setMySpots] = useState(null);
  const [spotBusy, setSpotBusy] = useState(false);
  useEffect(() => {
    if (!isPortal || !locs || locs.length || mySpots) return;
    sbFetch("policy_fields", { select: "county,latitude,longitude,acres,crop_year",
      filters: [`crop_year=gte.${CURRENT_CROP_YEAR - 1}`, "latitude=not.is.null", "longitude=not.is.null"] })
      .then((rows) => setMySpots(weatherSpotsFromFields(rows)))
      .catch(() => setMySpots([]));
  }, [isPortal, locs, mySpots]);
  const addSpots = (spots) => {
    if (!spots.length || spotBusy) return;
    setSpotBusy(true);
    sbInsert("weather_locations", spots.map((sp, i) => ({ name: sp.name, lat: sp.lat, lon: sp.lon, sort_order: (locs || []).length + i })))
      .then(() => { setErr(""); loadLocs(); })
      .catch(() => setErr("Could not save that location."))
      .finally(() => setSpotBusy(false));
  };

  // One fetch each, covering every location.
  useEffect(() => {
    if (!locs || !locs.length) { setFc(null); setGauge(null); return; }
    const pts = locs.map((l) => `${l.lat},${l.lon}`).join(";");
    let alive = true;
    setBusy(true); setErr("");
    // EACH PART LANDS ON ITS OWN. These were one Promise.all, so the page showed
    // nothing until the SLOWEST call returned — measured 2026-10-18 for three saved
    // locations: daily 16.4s, gauge 11.8s, nowcast 3.7s, fieldwork 1.1s. The audit
    // read the 16-second wait as "stuck on reading the models". Field-work windows
    // and the nowcast now appear as soon as they are back; `busy` tracks the
    // forecast itself, which is what the "reading the models" line is about.
    const part = (url) => fetch(url).then((r) => r.json());
    part(`/api/market?fn=precip&op=daily&pts=${encodeURIComponent(pts)}&days=4`)
      .then((d) => { if (!alive) return; if (d && d.ok) setFc(d); else setErr("Forecast unavailable."); })
      .catch(() => { if (alive) setErr("Forecast unavailable."); })
      .finally(() => { if (alive) setBusy(false); });
    part(`/api/market?fn=precip&op=gauge&pts=${encodeURIComponent(pts)}`)
      .then((g) => { if (alive && g && g.ok) setGauge(g); }).catch(() => {});
    // The nowcast is allowed to fail quietly. It is the one figure here that is
    // genuinely optional — the measured totals stand without it, and an error
    // banner over a missing nowcast would be worse than its absence.
    part(`/api/market?fn=precip&op=nowcast&pts=${encodeURIComponent(pts)}&minutes=60`)
      .then((n) => { if (alive) setNow(n && n.ok ? n : null); }).catch(() => { if (alive) setNow(null); });
    // Field-work windows are their own question (when can I get on the ground)
    // and their own source, so they load independently and may be absent.
    part(`/api/market?fn=precip&op=fieldwork&pts=${encodeURIComponent(pts)}&days=6`)
      .then((w) => { if (alive) setWork(w && w.ok ? w : null); }).catch(() => { if (alive) setWork(null); });
    return () => { alive = false; };
  }, [locs]);

  // ── THE ARCHIVE IS THE FAST PATH ─────────────────────────────────────────
  // The cron writes each cycle's forecast into weather_forecast_log, including the
  // calibrated exceedance probabilities that are far too expensive to read per page
  // load (one GRIB range-read per threshold per 12-hour block). So the day cards
  // read the DATABASE, and `op=daily` stays as the live fallback for a location
  // saved since the last cycle.
  //
  // Keyed by location so a card can find its own days without scanning.
  useEffect(() => {
    if (!locs || !locs.length) { setArch(null); return; }
    let alive = true;
    const todayET = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    sbFetch("weather_forecast_log", {
      select: "location_id,target_date,lead_days,inches,hi_in,om_inches,ec_inches,pop_pct,prob_quarter,prob_half,partial,hours_covered,run,made_at",
      filters: [`target_date=gte.${todayET}`],
      order: "target_date.asc",
    })
      .then((rows) => {
        if (!alive) return;
        const by = {};
        for (const r of rows || []) {
          // Several leads can exist for one date; the SMALLEST lead is the freshest
          // forecast of that day, which is the one to show.
          const cur = (by[r.location_id] = by[r.location_id] || {});
          if (!cur[r.target_date] || r.lead_days < cur[r.target_date].lead_days) cur[r.target_date] = r;
        }
        setArch(by);
      })
      .catch(() => { if (alive) setArch(null); });
    return () => { alive = false; };
  }, [locs]);

  // ── ALERTS ────────────────────────────────────────────────────────────────
  // The catalogue lives here rather than on the server so the form can label each
  // kind in the unit it is actually set in — a rain alert is inches, a frost alert
  // is degrees, a calm window is mph over hours. `window` marks the kinds that also
  // need a period. Kinds and their meanings match weather_alerts_kind_ck.
  const ALERT_KINDS = [
    { kind: "rain_forecast", label: "Rain forecast", unit: "in", def: 0.5, window: [24, 48, 72], windowDef: 24,
      how: (t, w) => `at least ${t}" forecast in the next ${w} hours` },
    { kind: "rain_received", label: "Rain received", unit: "in", def: 0.5, window: [12, 24, 72, 168], windowDef: 24,
      how: (t, w) => `at least ${t}" measured in the last ${w === 168 ? "7 days" : `${w} hours`}` },
    { kind: "rain_now", label: "Rain arriving", unit: "min", def: 30, window: null,
      how: (t) => `radar has rain reaching you within ${t} minutes` },
    { kind: "frost", label: "Frost", unit: "°F", def: 34, window: null,
      how: (t) => `a low at or below ${t}°F in the next 48 hours` },
    { kind: "heat", label: "Heat", unit: "°F", def: 90, window: null,
      how: (t) => `a high at or above ${t}°F in the next 48 hours` },
    { kind: "wind", label: "Wind", unit: "mph", def: 20, window: null,
      how: (t) => `any hour in the next 24 hours at or above ${t} mph` },
    { kind: "calm", label: "Calm window", unit: "mph", def: 10, window: [2, 3, 4, 6, 8], windowDef: 4,
      how: (t, w) => `${w}+ straight daylight hours under ${t} mph with no rain` },
    { kind: "dry_stretch", label: "Dry stretch", unit: "days", def: 3, window: null,
      how: (t) => `${t}+ days in a row with no measurable rain forecast` },
    { kind: "models_split", label: "Models split", unit: "in", def: 0.25, window: null,
      how: (t) => `the two models disagree on a day whose wetter figure is ${t}" or more` },
  ];
  const alertKind = (k) => ALERT_KINDS.find((x) => x.kind === k) || null;

  const loadAlerts = useCallback(() => {
    sbFetch("weather_alerts", {
      select: "id,location_id,kind,threshold,window_hours,email,active,cooldown_hours,last_fired_at",
      order: "created_at.asc,id.asc",
    })
      .then((rows) => setAlerts(rows || []))
      .catch(() => setAlerts([]));
  }, []);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // Anchored on the first saved location: the discussion is regional, so one fetch
  // covers the whole watchlist. The office is named in the UI so it is obvious whose
  // view this is, rather than implying it was written about one farm.
  useEffect(() => {
    if (!locs || !locs.length) { setSynopsis(null); return; }
    let alive = true;
    // Every saved point, each tagged with its own name. Anchoring on the first one
    // labelled a farm 45 miles away with the wrong town, and — worse — asked for
    // watches and warnings at the wrong coordinates.
    const pts = locs.map((l) => `${l.lat},${l.lon},${String(l.name || "").replace(/[;,]/g, " ")}`).join(";");
    fetch(`/api/market?fn=precip&op=synopsis&pts=${encodeURIComponent(pts)}`)
      .then((r) => r.json())
      .then((j) => { if (alive && j && j.ok) setSynopsis(j); })
      .catch(() => {});
    return () => { alive = false; };
  }, [locs]);

  // THE VERIFIED TRACK RECORD, COMPUTED FROM THE READER'S OWN ROWS.
  //
  // This used to call `?op=skill`, which reads with the SERVICE ROLE — so the
  // figure shown as "our track record" was computed across EVERY user's saved
  // locations. Not a data leak (the response is aggregates only, no names or
  // coordinates) but plainly wrong: a grower was being shown a hit rate that was
  // partly about someone else's farms.
  //
  // Reading `weather_forecast_log` directly puts RLS in charge — the rows that come
  // back are the caller's own by construction — and the arithmetic is a few lines,
  // so there is no reason to have asked a privileged endpoint for it. It also
  // removes an unauthenticated surface: the server op is now admin-only.
  const SKILL_TOL_IN = 0.1;
  useEffect(() => {
    let alive = true;
    sbFetch("weather_forecast_log", {
      select: "lead_days,inches,hi_in,om_inches,ec_inches,observed_in",
      filters: ["observed_in=not.is.null", "scored_at=not.is.null"],
      order: "target_date.desc,id.asc",
    })
      .then((rows) => {
        if (!alive) return;
        const ok = (rows || []).filter((r) => r.inches != null && r.observed_in != null)
          .map((r) => ({ lead: r.lead_days, f: Number(r.inches), o: Number(r.observed_in),
                         hi: r.hi_in == null ? null : Number(r.hi_in),
                         om: r.om_inches == null ? null : Number(r.om_inches),
                         ec: r.ec_inches == null ? null : Number(r.ec_inches) }));
        if (!ok.length) { setSkill({ tolIn: SKILL_TOL_IN, leads: [], agreement: null }); return; }
        const stat = (g) => {
          if (!g.length) return null;
          const within = g.filter((r) => Math.abs(r.f - r.o) <= SKILL_TOL_IN).length;
          return {
            n: g.length,
            withinPct: Math.round((within / g.length) * 100),
            biasIn: g.reduce((s, r) => s + (r.f - r.o), 0) / g.length,
            maeIn: g.reduce((s, r) => s + Math.abs(r.f - r.o), 0) / g.length,
          };
        };
        const leads = [...new Set(ok.map((r) => r.lead))].sort((a, b) => a - b).map((L) => {
          const g = ok.filter((r) => r.lead === L);
          const withHi = g.filter((r) => r.hi != null);
          return {
            leadDays: L, ...stat(g),
            wetCaseHeldPct: withHi.length
              ? Math.round((withHi.filter((r) => r.o <= r.hi).length / withHi.length) * 100) : null,
          };
        });
        // THREE TIERS, by how many of the two independent cross-checks land near
        // NBM. Measured 99% / 68% / 26% within 0.10" — the middle tier is a real
        // category that the old binary had to force to one side.
        const AGREE = 0.05;
        const withAll = ok.filter((r) => r.om != null && r.ec != null);
        const nearCount = (r) => [r.om, r.ec].filter((v) => Math.abs(r.f - v) <= AGREE).length;
        setSkill({
          tolIn: SKILL_TOL_IN,
          leads,
          tiers: withAll.length
            ? [2, 1, 0].map((near) => ({ near, ...(stat(withAll.filter((r) => nearCount(r) === near)) || { n: 0 }) }))
            : null,
        });
      })
      .catch(() => { if (alive) setSkill(null); });
    return () => { alive = false; };
  }, []);

  // Farm list, loaded only when the picker opens. Coordinates come straight off
  // the farmer row; farmers without them simply are not offered rather than
  // being listed and then failing on selection.
  //
  // Agent-side only. The portal never renders the farm picker and never runs this
  // fetch — the guard is here as well as at the render because a customer NAME LIST
  // is agency material regardless of who can read it, and because a portal user is
  // not always a farmer: a limited agent's own RLS legitimately returns their granted
  // customers, so RLS alone would put a name list on a portal screen. (It was also
  // the only thing standing between the OLD admin-token preview and all 282 names —
  // the same shape as the Field Mapper leak. Preview holds the farmer's token now,
  // but the guard is load-bearing for its own reason.) Hide the surface, then guard
  // the fetch.
  useEffect(() => {
    if (isPortal || !adding || farmOpts) return;
    sbFetch("farmers", { select: "id,name,lat,lon", filters: ["lat=not.is.null", "lon=not.is.null"], order: "name.asc,id.asc" })
      .then((rows) => setFarmOpts(rows || []))
      .catch(() => setFarmOpts([]));
  }, [adding, farmOpts, isPortal]);

  useEffect(() => { if (adding && !Lp) loadLeaflet().then(setLp).catch(() => {}); }, [adding, Lp]);

  // A TYPED COORDINATE IS EMPTY OR IT IS A NUMBER — `Number("")` is 0, and 0 is a
  // real place. Every check below went through `Number.isFinite(Number(x))`, which
  // says an untouched form is the point 0°N 0°E: open water in the Gulf of Guinea,
  // ~1,000 miles off Ghana. That is where the picker sent you.
  const coord = (v) => { const s = String(v == null ? "" : v).trim(); return s === "" ? NaN : Number(s); };

  // Click-to-place picker. Kept deliberately small — it exists to answer "where
  // is this", not to be a second Field Mapper.
  useEffect(() => {
    if (!adding || !Lp || !pickMapEl.current || pickMapRef.current) return;
    const map = Lp.map(pickMapEl.current, { zoomControl: true, attributionControl: false })
      .setView([42.9, -77.85], 8);
    // OPEN WHERE THEIR OTHER GAUGES ARE. Adding a second point is nearly always
    // near the first, so starting on the whole book's footprint means panning and
    // zooming to ground you were already looking at on the tab behind this one.
    // Read at open only — the map is created once and this is its starting view,
    // not something that should move under a click. Nothing saved yet keeps the
    // WNY view above, which is the book's own footprint.
    const anchors = (locs || [])
      .map((l) => [coord(l.lat), coord(l.lon)])
      .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b));
    if (anchors.length === 1) map.setView(anchors[0], 11);
    else if (anchors.length > 1) {
      try { map.fitBounds(anchors, { padding: [26, 26], maxZoom: 12 }); } catch (e) {}
    }
    Lp.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 18 }).addTo(map);
    // Roads and place names on top of the imagery. Bare satellite is close to
    // unusable for CHOOSING a spot — one green field looks like the next, and
    // you cannot tell which side of town you are on. Same two reference layers
    // the contour map uses.
    Lp.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 18, opacity: 0.9 }).addTo(map);
    Lp.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 18, opacity: 0.9 }).addTo(map);
    map.on("click", (ev) => {
      const { lat, lng } = ev.latlng;
      setForm((f) => ({ ...f, lat: lat.toFixed(4), lon: lng.toFixed(4) }));
    });
    pickMapRef.current = map;
    setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 60);
    return () => { try { map.remove(); } catch (e) {} pickMapRef.current = null; pickMarkRef.current = null; };
    // `locs` is deliberately not a dependency: it is the OPENING view, and this
    // effect's cleanup destroys the map — re-running it on a locations refresh
    // would tear the picker down mid-click.
  }, [adding, Lp]); // eslint-disable-line

  // Keep the pin and the view in step with whatever set the coordinates — the
  // farm dropdown and the map click both write the same two fields.
  const pickLat = coord(form.lat), pickLon = coord(form.lon);
  useEffect(() => {
    const map = pickMapRef.current; if (!map || !Lp) return;
    const lat = pickLat, lon = pickLon;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      if (pickMarkRef.current) { try { map.removeLayer(pickMarkRef.current); } catch (e) {} pickMarkRef.current = null; }
      return;
    }
    if (!pickMarkRef.current) {
      pickMarkRef.current = Lp.circleMarker([lat, lon], { radius: 7, color: "#fff", weight: 2.5, fillColor: "#1f3320", fillOpacity: .9 }).addTo(map);
    } else {
      try { pickMarkRef.current.setLatLng([lat, lon]); } catch (e) {}
    }
    try { map.setView([lat, lon], Math.max(map.getZoom(), 11)); } catch (e) {}
  }, [pickLat, pickLon, Lp]);

  // Both writes are LIVE in preview, and land on the previewed farmer's own list —
  // `created_by` defaults to `auth.uid()`, which is them. That is the point: an agent
  // on the phone can drop a gauge on the grower's yard and they will have it when
  // they hang up. (Under the OLD admin-token preview this same call would have
  // written to KEVIN'S list, which is why it used to refuse.)
  const addLoc = () => {
    const name = form.name.trim(), lat = coord(form.lat), lon = coord(form.lon);
    if (!name || !Number.isFinite(lat) || !Number.isFinite(lon)) { setErr("Name, latitude and longitude are all required."); return; }
    sbInsert("weather_locations", [{ name, lat, lon, sort_order: (locs || []).length }])
      .then(() => { setForm({ name: "", lat: "", lon: "" }); setAdding(false); setErr(""); loadLocs(); })
      .catch(() => setErr("Could not save that location."));
  };
  const delLoc = (id) => {
    sbDelete("weather_locations", `id=eq.${id}`)
      .then(loadLocs)
      .catch(() => setErr("Could not remove that location."));
  };

  const dates = fc && fc.locations && fc.locations[0] ? fc.locations[0].days : [];
  const dayLabel = (d) => {
    const dt = new Date(`${d.date}T12:00:00Z`);
    return {
      wd: dt.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }),
      md: dt.toLocaleDateString("en-US", { month: "numeric", day: "numeric", timeZone: "UTC" }),
    };
  };
  // A day can be 40% likely and still average a trace — the amount is an expected
  // value across the blend, dragged down by the dry members. Printing 0.00 next to
  // a real chance reads as a contradiction.
  const amtText = (d) => d.inches >= 0.01 ? `${d.inches.toFixed(2)}"` : (d.popPct != null && d.popPct >= 20 ? "trace" : "—");

  // ── Days for one location: archive first, live endpoint as the fallback ────
  // The archive carries the calibrated exceedance probabilities; op=daily does not,
  // because reading them per request costs a GRIB range-read per threshold per
  // block. A location saved since the last cycle has no archive yet, so it falls
  // back and simply shows no probability columns rather than an empty card.
  // ONE VINTAGE OF EACH MODEL PER SCREEN.
  //
  // NBM and its probabilities come from the archive (written 4x/day by cron, and the
  // header stamps which run). The two CROSS-CHECKS are taken from the live fieldwork
  // payload instead — the same fetch that draws the hourly strips and the day table
  // below them. Reading them from the archive put "G 0.00" beside a table row of
  // 0.40" for the same model on the same screen, because the archive was six hours
  // older than the page. Whatever the cross-check says here, the sections underneath
  // say too.
  const liveXCheck = (li, date) => {
    const w = work && work.locations ? work.locations[li] : null;
    const d = w && w.days ? w.days.find((x) => x.date === date) : null;
    if (!d) return null;
    return { om: d.precipIn == null ? null : Number(d.precipIn), ec: d.ecIn == null ? null : Number(d.ecIn) };
  };

  const daysFor = (locId, li) => {
    const rows = arch && arch[locId] ? Object.values(arch[locId]) : null;
    if (rows && rows.length) {
      return rows
        .sort((a, b) => (a.target_date < b.target_date ? -1 : 1))
        .map((r) => ({
          date: r.target_date,
          inches: r.inches == null ? 0 : Number(r.inches),
          hiIn: r.hi_in == null ? null : Number(r.hi_in),
          // The two independent cross-checks, LIVE where available so they match the
          // hourly sections below; the archived values are the fallback. Null means
          // no check for that day — the strip shows no marker rather than agreement.
          omInches: (liveXCheck(li, r.target_date) || {}).om
            ?? (r.om_inches == null ? null : Number(r.om_inches)),
          ecInches: (liveXCheck(li, r.target_date) || {}).ec
            ?? (r.ec_inches == null ? null : Number(r.ec_inches)),
          popPct: r.pop_pct,
          probQuarter: r.prob_quarter,
          probHalf: r.prob_half,
          partial: r.partial,
          hoursCovered: r.hours_covered,
          fromArchive: true,
        }));
    }
    const f = fc && fc.locations ? fc.locations[li] : null;
    return f && f.days ? f.days.map((d) => ({ ...d, probQuarter: null, probHalf: null, omInches: null, fromArchive: false })) : [];
  };

  const dayName = (iso) => {
    const dt = new Date(`${iso}T12:00:00Z`);
    const todayET = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    const tomorrow = new Date(Date.now() + 86400000).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    if (iso === todayET) return "today";
    if (iso === tomorrow) return "tomorrow";
    return dt.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
  };

  // ── THE HEADLINE ──────────────────────────────────────────────────────────
  // Two short sentences: what is happening, then the next thing that matters. This
  // is the answer the page exists to give, so it goes first and in plain words —
  // everything below it is the evidence.
  //
  // The "next rain" test is deliberately on the CALIBRATED probability of a
  // quarter inch rather than on the blend mean. A 0.18" mean is not a fact anyone
  // can act on; "40% chance of a quarter inch" is, and on 2026-08-08 the mean was
  // the number that read as "nothing" on a day that delivered 0.93".
  // RECEIVED ONLY. The forecast half of this sentence moved to the Forecast tab
  // along with everything else that is a prediction — this page is the rain gauge,
  // and mixing a measurement and a forecast in one sentence is what made it unclear
  // which of the two the number underneath belonged to.
  const headlineFor = (g) => {
    const todayIn = g && g.today != null ? g.today : null;
    if (g && g.lastHour > 0) {
      return `Raining now${todayIn ? ` — ${todayIn.toFixed(2)}" so far today` : ""}.`;
    }
    if (todayIn != null && todayIn >= 0.01) return `${todayIn.toFixed(2)}" today, nothing falling right now.`;
    return "Dry so far today.";
  };

  const card = { background: T.surface, border: `1px solid ${T.line}`, borderRadius: 12, padding: isMobile ? 12 : 16 };
  const th = { fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint, textAlign: "left", padding: "0 8px 6px" };

  // ── viz tokens ────────────────────────────────────────────────────────────
  // Rainfall is ONE measure, so it gets ONE hue. Amount is carried by bar height
  // and meter fill; colouring by value as well would double-encode what the
  // geometry already shows and burn the only free channel. The unfilled meter
  // track is a lighter step of the SAME hue so state reads across the whole bar.
  const RAIN = "#1f5b8f";
  // Track is the lightest step of the same hue that still clears 2:1 against the
  // card. The obvious pale blue (#dfeaf4) measured 1.19:1 — on a dry day the
  // meter would have been invisible, so you could not see there was a gauge at
  // all, only an absence. Checked with the palette validator, not by eye.
  const RAIN_TRACK = "#8ab3d5";
  // Partial-day fill: the rain hue striped with the card surface. One hue, so it
  // never reads as a different amount — the stripes read as "part of a day".
  const HATCH = `repeating-linear-gradient(45deg, ${RAIN} 0 6px, ${T.surface} 6px 9px)`;
  const GRID = T.lineSoft;
  // Proportional figures on standalone values — tabular gives every digit the
  // width of a zero, which makes a number like 0.11 look loose at display size.
  // Columns of numbers (the table tab, axis ticks) keep tabular so they align.
  const bigNum = { fontFamily: "inherit", fontVariantNumeric: "proportional-nums", fontWeight: 800, letterSpacing: "-.01em" };

  const GAUGE_WINDOWS = [
    { key: "today", short: "Today", long: "Today so far" },
    { key: "h12",   short: "12h",   long: "Last 12 hours" },
    { key: "h24",   short: "24h",   long: "Last 24 hours" },
    { key: "h72",   short: "72h",   long: "Last 72 hours" },
    { key: "d7",    short: "7 day", long: "Last 7 days" },
  ];
  // Round the meter's top up to a readable step. The scale has to follow the
  // WINDOW — a 7-day total runs several inches, and holding it to the 1" scale a
  // single day wants would peg every card at full and say nothing.
  const niceTop = (v) => {
    for (const s of [0.25, 0.5, 1, 1.5, 2, 3, 4, 6, 8, 12, 16]) if (v <= s) return s;
    return Math.ceil(v);
  };

  const tabBtn = (k, label) => (
    <button key={k} onClick={() => setTab(k)}
      style={{ cursor: "pointer", fontFamily: "inherit", border: "none", borderRadius: 8, padding: "7px 15px",
               fontSize: 12.5, fontWeight: 800, background: tab === k ? T.inkSoft : "transparent",
               color: tab === k ? "#fff" : T.body }}>{label}</button>
  );

  return (
    <div style={embedded ? {} : { padding: isMobile ? 12 : 20, maxWidth: 1180, margin: "0 auto" }}>
      {!embedded && <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        <h1 style={{ ...uiPageTitle(isMobile) }}>Weather</h1>
        <span style={{ fontSize: 12, color: T.muted }}>radar now, what fell, and what is coming</span>
        {/* The link to the backward-looking half. Gated on BOTH the effective
            identity and the presence of onNav, so it fails closed: a portal
            viewer cannot reach `wxrecords` at all, and a mount that supplies no
            navigation renders no link rather than a dead click. */}
        {isCurrentUserAdmin() && onNav && (
          <button onClick={() => onNav("wxrecords")} title="Weather Records — any point, any date range, printable for a claim"
            style={{ cursor: "pointer", fontFamily: "inherit", border: "1px solid #c8d8c8", borderRadius: 8,
                     padding: "4px 11px", fontSize: 11.5, fontWeight: 700, background: "#fff", color: T.body }}>
            📊 Weather Records →
          </button>
        )}
      </div>}
      {/* Provenance line. The RECEIVED figure is two measurements deep now —
          gauge-corrected through `asOf`, radar-only for the live edge — and saying
          so is the difference between a number you can act on and a number you
          quietly stop believing. */}
      <div style={{ fontSize: 11, color: T.faint, marginBottom: 14 }}>
        {tab === "radar" ? (
          <>Observed NEXRAD through now, then HRRR forward — measured and modelled are labelled on the strip</>
        ) : (
          <>
            Received = MRMS gauge-corrected radar{gauge && gauge.asOf ? ` through ${new Date(gauge.asOf).toLocaleString("en-US", { weekday: "short", hour: "numeric", timeZone: "America/New_York" })}` : ""}
            {gauge && gauge.liveMinutes > 0 && (
              <span style={{ color: "#7a5a20" }}>
                {" + "}the last {gauge.liveMinutes < 60 ? `${gauge.liveMinutes} min` : `${(gauge.liveMinutes / 60).toFixed(gauge.liveMinutes % 60 ? 1 : 0)}h`} live from radar
              </span>
            )}
            {" · "}Forecast = NBM{fc && fc.run ? ` ${fc.run.slice(8)}Z run` : ""}
            {gauge && gauge.windows && gauge.windows.some((w) => !w.complete) && (
              <span style={{ color: T.danger, fontWeight: 700 }}>
                {" · "}some received totals are incomplete (a radar file was unavailable) and read low
              </span>
            )}
          </>
        )}
      </div>

      {err && <div style={{ ...card, borderColor: "#e6c9c4", background: "#fdf3f1", color: T.danger, fontSize: 12, marginBottom: 12 }}>{err}</div>}

      {/* ── WHAT'S GOING ON ────────────────────────────────────────────────
          Sits ABOVE the tabs on purpose. Every tab under it is a number we
          computed; this is the one thing that says WHY, and it should frame all
          of them rather than become a sixth island on a page that already felt
          like separate parts.

          It is the NWS forecaster's own text, quoted and attributed, never
          paraphrased by us. On 2026-08-10 our own headline read "Likely dry
          through Thursday" — true of the point probability — while the office
          covering these fields was writing about a warm front and a marginal
          severe risk. The numbers could not say that, and no amount of ours
          would have. */}
      {synopsis && (() => {
        // The band follows whichever farm is selected on the Forecast tab; on the
        // other tabs, where every location is on screen at once, it falls back to the
        // first. Named per point, so switching to Gasport says Gasport.
        const sel = (tab === "windows" && locs && locs.length)
          ? locs[Math.min(fcLoc, locs.length - 1)] : (locs && locs[0]);
        const selName = sel ? sel.name : null;
        const ptInfo = (synopsis.points || []).find((p) => p.id === selName) || (synopsis.points || [])[0] || null;
        const disc = (synopsis.discussions || []).find((d) => !selName || (d.covers || []).includes(selName))
          || (synopsis.discussions || [])[0] || null;
        // Alerts anywhere on the watchlist are worth showing, not just at the
        // selected farm — a warning over another of your fields still matters.
        const alerts = synopsis.alerts || [];
        if (!disc && !alerts.length) return null;
        return (
        <div style={{ border: `1px solid ${alerts.length ? "#e6c9a8" : T.line}`,
                      background: alerts.length ? "#fdf8ee" : T.surface,
                      borderRadius: 12, padding: isMobile ? "10px 12px" : "12px 16px", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase",
                           color: T.faint, whiteSpace: "nowrap" }}>
              What’s going on
            </span>
            {/* Live watches and warnings lead — a severe thunderstorm watch is a
                fact about today that no probability on this page conveys. */}
            {alerts.slice(0, 3).map((a) => (
              <span key={`${a.at}|${a.event}`} title={[a.at, a.headline].filter(Boolean).join(" — ")}
                style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".03em", padding: "2px 8px", borderRadius: 6,
                         background: /warning/i.test(a.event) ? "#fdeceb" : "#f6eeda",
                         color: /warning/i.test(a.event) ? "#a33" : "#7a5a20", whiteSpace: "nowrap" }}>
                {a.event}{a.at ? ` · ${a.at}` : ""}
              </span>
            ))}
            <button onClick={() => setSynOpen((v) => !v)}
              title={synOpen ? "Hide the discussion" : "Show the discussion"}
              style={{ marginLeft: "auto", cursor: "pointer", fontFamily: "inherit", border: "none",
                       background: "transparent", padding: 0, fontSize: 11, fontWeight: 800, color: T.faint,
                       display: "inline-flex", alignItems: "center", gap: 4 }}>
              {synOpen ? "hide" : "show"}
              <span style={{ fontSize: 10 }}>{synOpen ? "▲" : "▼"}</span>
            </button>
          </div>

          {synOpen && disc && (
            <>
              <div style={{ fontSize: isMobile ? 12.5 : 13, color: T.ink, lineHeight: 1.55, marginTop: 7,
                            whiteSpace: "pre-wrap" }}>
                {disc.text}
              </div>
              <div style={{ fontSize: 10, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
                {/* Attribution is the point: this is a named office's judgement, not
                    ours, and it describes the REGION while everything below is a
                    number for one point. Saying so is what keeps the two from
                    reading as a contradiction. */}
                NWS {disc.office}{ptInfo && ptInfo.place ? ` · ${ptInfo.place}` : ""} —{" "}
                {disc.section.toLowerCase()}, issued{" "}
                {new Date(disc.issued).toLocaleString("en-US",
                  { weekday: "short", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })}.
                {" "}Their words, unedited. This describes the whole region — the numbers below are for
                one point, so a scattered-storm day can read “active” here and still be unlikely at your farm.
              </div>
            </>
          )}
          {synOpen && !disc && (
            <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
              No forecast discussion available right now.
            </div>
          )}
        </div>
        );
      })()}

      {/* One control row above everything it scopes. Table is kept as the
          accessible twin of the visual views, not as a legacy leftover.
          Radar is offered even with nothing saved, because it reads no per-user
          data — it is coordinates and public weather. The other four are watchlist
          views and stay gated on having a watchlist, in preview exactly as in a real
          session: preview holds the farmer's own token, so an empty strip here means
          they have saved nothing, which is a fact worth seeing rather than one to
          hide behind a disclaimer. */}
      <div style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 10,
                    background: T.surfaceAlt, border: `1px solid ${T.line}`, marginBottom: 12 }}>
        {tabBtn("radar", "🌧️ Radar")}
        {/* One tab per QUESTION, not one per data source. "Farms" answers what fell
            and what's coming in one card, so the old Gauges/Forecast split — which
            made you visit two screens to assemble one picture — is gone. */}
        {locs && locs.length > 0 && tabBtn("farms", "📍 Farms")}
        {/* Two pages, two kinds of number. Farms is the RAIN GAUGE — what fell,
            over the window you pick. Forecast is everything predicted: the day
            outlook, the agreement marker, the hourly strips, the nowcast. Mixing
            them on one card made it unclear which kind any given figure was.
            (Key stays `windows` — it is only a label.) */}
        {locs && locs.length > 0 && tabBtn("windows", "📅 Forecast")}
        {locs && locs.length > 0 && tabBtn("alerts", "🔔 Alerts")}
        {locs && locs.length > 0 && tabBtn("table", "Table")}
      </div>

      {/* Saving as someone else is worth saying out loud ONCE, where the save
          controls are. The orange frame already says changes are live; this names
          what changes — a gauge added here is on their account, under their login,
          and the alert emails go to whatever address is on the row. */}
      {preview && (
        <div style={{ fontSize: 11.5, color: "#7a5a20", background: "#fdf8ee", border: "1px solid #e6c9a8",
                      borderRadius: 8, padding: "7px 11px", marginBottom: 12, lineHeight: 1.45 }}>
          This is <b>their</b> watchlist, read with their login. Anything you add or remove here is saved
          on their account.
        </div>
      )}

      {tab === "radar" ? (
        <WeatherRadarMap
          isMobile={isMobile}
          locs={locs || []}
          // Tapping the map and hitting save drops straight into the add form with
          // the coordinates already filled — the same two fields the map picker
          // writes, so there is one save path, not two.
          onPickPoint={(p) => {
            setForm({ name: "", lat: p.lat.toFixed(4), lon: p.lon.toFixed(4) });
            setAdding(true);
          }}
        />
      ) : locs === null ? (
        <div style={{ ...card, color: T.muted, fontSize: 13 }}>Loading saved locations…</div>
      ) : !locs.length ? (
        <div style={{ ...card, color: T.body, fontSize: 13 }}>
          No saved locations yet. Add one below — a farm, a yard, anywhere you want a rain number for.
        </div>
      ) : tab === "farms" ? (
        /* GAUGES — the job here is a headline number per location, so the form is
           a stat tile with a meter, not a chart. Deliberately NOT hero-sized: a
           view gets one hero figure and this view has many locations. */
        (() => {
        // Offer only windows the response actually carries. Hardcoding the list
        // meant a newly-added window (12h) rendered as a blank card whenever the
        // cached response predated it — the UI promised a number the data could
        // not supply.
        const avail = gauge && gauge.windows && gauge.windows.length
          ? GAUGE_WINDOWS.filter((w) => gauge.windows.some((x) => x.key === w.key))
          : GAUGE_WINDOWS;
        const sel = avail.find((w) => w.key === gWin) || avail[0] || GAUGE_WINDOWS[0];
        // ONE scale for every card, derived from the biggest reading in the
        // current window. Scaling each card to its own value would make the
        // wettest and driest locations look identical — the exact comparison a
        // watchlist exists to make.
        const vals = (gauge && gauge.locations ? gauge.locations : []).map((g) => (g && g[sel.key] != null ? g[sel.key] : 0));
        const top = niceTop(Math.max(0.25, ...vals));
        return (
        <>

        <div style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 9, background: T.surfaceAlt,
                      border: `1px solid ${T.line}`, marginBottom: 12 }}>
          {avail.map((w) => (
            <button key={w.key} onClick={() => setGWin(w.key)} title={w.long}
              style={{ cursor: "pointer", fontFamily: "inherit", border: "none", borderRadius: 7, padding: "5px 12px",
                       fontSize: 11.5, fontWeight: 800, background: gWin === w.key ? RAIN : "transparent",
                       color: gWin === w.key ? "#fff" : T.body }}>{w.short}</button>
          ))}
        </div>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(268px, 1fr))" }}>
          {locs.map((l, i) => {
            const g = gauge && gauge.locations ? gauge.locations[i] : null;
            const today = g && g[sel.key] != null ? g[sel.key] : null;
            const scale = top;
            const pctFull = today == null ? 0 : Math.max(0, Math.min(1, today / scale));
            return (
              <div key={l.id} style={{ ...card }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: T.ink }}>{l.name}</div>
                  <button onClick={() => delLoc(l.id)} title={`Remove ${l.name}`}
                    style={{ cursor: "pointer", fontFamily: "inherit", border: "none", background: "transparent",
                             fontSize: 10.5, fontWeight: 700, color: T.faint }}>remove</button>
                </div>
                <div style={{ ...uiNum, fontSize: 10.5, color: T.faint, marginBottom: 10 }}>
                  {Number(l.lat).toFixed(3)}, {Number(l.lon).toFixed(3)}
                </div>

                {/* THE ANSWER, FIRST. Everything under it is the evidence. */}
                {g && (
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, lineHeight: 1.5, marginBottom: 12 }}>
                    {headlineFor(g)}
                  </div>
                )}

                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint }}>
                  {sel.long}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ ...bigNum, fontSize: 34, lineHeight: 1.05, color: today ? RAIN : T.faint }}>
                    {today == null ? "—" : today.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>in</span>
                </div>

                {/* Fill and track are steps of the SAME hue, so the empty part
                    still reads as gauge rather than as a gap. */}
                <div style={{ height: 10, borderRadius: 5, background: RAIN_TRACK, marginTop: 8, overflow: "hidden" }}>
                  <div style={{ width: `${pctFull * 100}%`, height: "100%", borderRadius: 5, background: RAIN }} />
                </div>
                <div style={{ ...uiNum, fontSize: 10, color: T.faint, marginTop: 3 }}>0 – {scale.toFixed(2)}"</div>

                {/* THE LIVE SHARE. This is the line that fixes "it says 0.00 while
                    it's pouring": the gauge-corrected product runs ~1.5h behind, so
                    the newest rain arrives from radar alone. It is inside the total
                    above, and stated here because a radar-only figure can run high
                    in heavy convection and should not masquerade as a gauge. */}
                {g && g.live > 0 && (
                  <div style={{ fontSize: 10, color: "#7a5a20", marginTop: 6, lineHeight: 1.35 }}
                    title="MRMS Pass1 hourly + RadarOnly 15-minute accumulations. Radar only — the gauge correction lands an hour or two later and can move this number.">
                    incl. <b style={{ ...uiNum }}>{g.live.toFixed(2)}"</b> in the last{" "}
                    {gauge.liveMinutes < 60 ? `${gauge.liveMinutes} min` : `${(gauge.liveMinutes / 60).toFixed(gauge.liveMinutes % 60 ? 1 : 0)}h`}
                    {" "}· radar only, not yet gauge-corrected
                  </div>
                )}

                {/* THE ROLLING HOUR — a measurement, so it stays. A complete 1-hour
                    window from the 2-minute radar product (~4 min old), which is how
                    "is it raining on me right now" gets answered while the totals
                    above are up to 18 minutes behind.

                    The nowcast and the four-day strip both moved to the Forecast tab.
                    This page is the rain gauge: what fell, over the window you pick.
                    Mixing a measurement and a projection in one card made it unclear
                    which of the two any given number was. */}
                {g && g.lastHour != null && (
                  <div style={{ marginTop: 10, paddingTop: 9, borderTop: `1px solid ${T.lineSoft}`,
                                display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".06em",
                                   textTransform: "uppercase", color: T.faint }}
                      title={gauge && gauge.lastHourAsOf
                        ? `Rolling hour ending ${new Date(gauge.lastHourAsOf).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} — radar only`
                        : "Rolling 1-hour radar accumulation"}>
                      Last hour
                    </span>
                    <span style={{ ...uiNum, fontSize: 13, fontWeight: 800, color: g.lastHour > 0 ? RAIN : T.faint }}>
                      {g.lastHour.toFixed(2)}"
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
        );
        })()
      ) : tab === "windows" ? (
        /* ── HOURS ─────────────────────────────────────────────────────────
           Rain, wind and temperature hour by hour, drawn so the windows can be
           SEEN. It deliberately names none of them.

           The first version of this screen rated each day "workable" against a
           threshold of its own invention and listed "spray windows" filtered to
           10 mph and 45F. Both were wrong in the same way: the badge landed on
           every row — including one carrying 0.32" of rain — so it asserted a
           judgement while informing nothing, and the spray filter hid the hours
           it rejected behind limits that vary by chemical, tank mix and operator.
           Whether an hour is a window depends on things this page cannot know.

           So: two strips per day, one measure each, and a table of counts and
           ranges. Every number is a measurement; the reading is the user's. */
        (() => {
          const li = Math.min(fcLoc, locs.length - 1);
          const l = locs[li];
          const w = work && work.locations ? work.locations[li] : null;
          const fmtDay = (iso) => new Date(`${iso.slice(0, 10)}T12:00:00Z`)
            .toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric", timeZone: "UTC" });
          const fmtHour = (iso) => new Date(`${iso}:00`).toLocaleTimeString("en-US", { hour: "numeric" });

          // Rain: one hue, light to dark by amount. Wind: its own hue, because it
          // is a different measure and must not read as rainfall.
          const rainCell = (mm) => {
            if (mm == null || mm < 0.01) return "transparent";
            if (mm < 0.02) return "#cfe0ee";
            if (mm < 0.05) return "#9dc0dd";
            if (mm < 0.10) return "#6b9fcb";
            if (mm < 0.20) return "#3d7fb4";
            return RAIN;
          };
          const windCell = (v) => {
            if (v == null) return "transparent";
            if (v < 5) return "#e8ece6";
            if (v < 10) return "#c3d0bd";
            if (v < 15) return "#9bb192";
            if (v < 20) return "#74926a";
            return "#4d6d45";
          };
          return (
            <>
              {locs.length > 1 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                  {locs.map((x, k) => (
                    <button key={x.id} onClick={() => setFcLoc(k)}
                      style={{ cursor: "pointer", fontFamily: "inherit", borderRadius: 8, padding: "6px 13px",
                               fontSize: 12.5, fontWeight: 800,
                               border: `1px solid ${k === li ? T.inkSoft : T.lineStrong}`,
                               background: k === li ? T.inkSoft : T.surface,
                               color: k === li ? "#fff" : T.body }}>{x.name}</button>
                  ))}
                </div>
              )}
              <div style={{ ...card }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: T.ink }}>{l.name}</div>

                {/* THE NOWCAST'S ACTUAL ANSWER for this location — is rain about to
                    arrive here, and how much. It lives on this tab because it is a
                    projection, not a measurement.

                    This readout went missing for one commit: splitting the gauge card
                    from the forecast moved the nowcast's PROVENANCE line here but left
                    its per-location output behind, so the endpoint kept being fetched
                    (several seconds of GRIB decoding per page load) and nothing showed
                    it. A fetch with no consumer is a bug in both directions. */}
                {(() => {
                  const n = now && now.locations ? now.locations[li] : null;
                  if (!n) return null;
                  const horizon = now.horizonMin || 0;
                  if (horizon < 5) {
                    return (
                      <div style={{ fontSize: 11.5, color: T.faint, marginTop: 5 }}
                        title="Radar motion is measured from consecutive MRMS scans. With too little echo in view, or a field not moving coherently, no projection is served rather than a made-up one.">
                        No short-term projection right now.
                      </div>
                    );
                  }
                  const wet = n.startsInMin != null;
                  return (
                    <div style={{ fontSize: 12.5, color: T.body, marginTop: 5, lineHeight: 1.5 }}>
                      {wet ? (
                        <>
                          <b style={{ color: RAIN }}>
                            {n.startsInMin === 0 ? "Raining here now" : `Rain here in about ${n.startsInMin} min`}
                          </b>
                          {n.expectedIn > 0 && <> · <b style={{ ...uiNum }}>{n.expectedIn.toFixed(2)}"</b> expected</>}
                          <span style={{ color: T.faint }}> over the next {horizon} min</span>
                        </>
                      ) : (
                        <span style={{ color: T.muted }}>Nothing headed here in the next {horizon} min.</span>
                      )}
                    </div>
                  );
                })()}

                {/* THE ACTUAL MESSAGE, IN A SENTENCE.
                    A row of percentages reading 6 / 11 / 2 / 7 is a confident
                    forecast — "almost certainly dry" — but it does not read as one
                    until someone says it. On days we have put the chance of a quarter
                    inch under 10%, that much rain arrived 2% of the time across 199
                    archived days, so this is a claim the record supports. */}
                {(() => {
                  const ds = daysFor(l.id, li).slice(0, 4);
                  const withP = ds.filter((d) => (d.probQuarter ?? d.popPct) != null);
                  if (!withP.length) return null;
                  const top = withP.reduce((m, d) => ((d.probQuarter ?? d.popPct) > (m.probQuarter ?? m.popPct) ? d : m));
                  const topP = top.probQuarter ?? top.popPct;
                  // The wettest day by CHANCE OF GETTING WET, which is a different day
                  // from the wettest by amount and a different question.
                  const wetDay = ds.reduce((m, d) => ((d.popPct ?? 0) > (m.popPct ?? 0) ? d : m), ds[0]);
                  const wetP = wetDay.popPct ?? 0;
                  const dayName2 = (d) => new Date(`${d.date}T12:00:00Z`)
                    .toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
                  const lastDay = new Date(`${ds[ds.length - 1].date}T12:00:00Z`)
                    .toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });

                  // THREE STATES, because two numbers govern this and they disagree
                  // often. A day can be 63% likely to wet you and 6% likely to give
                  // you a quarter inch — calling that "likely dry", as this line did,
                  // is wrong for anyone deciding whether to spray or cut.
                  const showery = wetP >= 40 && topP < 20;
                  const verdict = showery ? { t: "Showers around, but light", c: "#a3762a" }
                    : topP >= 45 ? { t: "Rain is likely", c: RAIN }
                    : topP >= 20 ? { t: "A wetting rain is possible", c: "#a3762a" }
                    : wetP >= 25 ? { t: "Mostly dry", c: "#2d6a2d" }
                    : { t: "Likely dry", c: "#2d6a2d" };
                  return (
                    <div style={{ fontSize: 13, color: T.body, marginTop: 8, lineHeight: 1.5 }}>
                      <b style={{ color: verdict.c }}>{verdict.t} through {lastDay}.</b>{" "}
                      {showery ? (
                        <>{dayName2(wetDay)} carries a <b style={{ ...uiNum }}>{wetP}%</b> chance of getting
                          wet, but only <b style={{ ...uiNum }}>{wetDay.probQuarter ?? 0}%</b> of a quarter inch —
                          enough to stop you, not enough to soak the ground.</>
                      ) : topP >= 20 ? (
                        <>{dayName2(top)} is the day to watch — <b style={{ ...uiNum }}>{topP}%</b> chance of a
                          quarter inch{top.hiIn != null && top.hiIn > top.inches + 0.05
                            ? <>, up to <b style={{ ...uiNum }}>{top.hiIn.toFixed(2)}"</b> if it hits</> : ""}.</>
                      ) : (
                        <>The wettest day is {dayName2(top)}: <b style={{ ...uiNum }}>{wetDay.popPct ?? 0}%</b> chance
                          of any rain, <b style={{ ...uiNum }}>{topP}%</b> of a quarter inch.</>
                      )}
                    </div>
                  );
                })()}

                {/* ── THE NEXT FOUR DAYS ────────────────────────────────────────
                    BAR HEIGHT IS THE CHANCE OF RAIN, NOT THE AMOUNT.

                    It used to be the amount, with the wet case drawn behind it — and
                    that made the least likely day the loudest thing on the chart.
                    Tuesday sat at 0.00" likely and an 11% chance of a quarter inch,
                    but carried a 0.40" wet case, so its pale bar set the scale and the
                    week read as "rain Tuesday". A 1-in-10 tail was drawing the
                    picture.

                    Height is now the calibrated probability, which is the number this
                    forecast is actually good at: measured over 360 archived days it
                    scores a Brier skill of 0.50 against climatology, and when it says
                    under 10% a quarter inch arrives 2% of the time. Amount rides
                    underneath as text, where an unreliable number belongs. An 11% day
                    is now visibly a stub. */}
                {(() => {
                  const ds = daysFor(l.id, li).slice(0, 4);
                  if (!ds.length) return null;
                  const PLOT = 76;
                  const chance = (d) => d.probQuarter ?? d.popPct ?? 0;
                  const amountText = (d) => {
                    if (d.inches >= 0.01) return `${d.inches.toFixed(2)}"`;
                    return "—";
                  };
                  return (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.lineSoft}` }}>
                      {/* The axis says what the bars ARE, above them, before they are
                          read. A caption underneath did not do it — the first question
                          the chart got was still "is the bar chance or amount?", which
                          is the one thing a reader must not have to ask. */}
                      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em",
                                    textTransform: "uppercase", color: T.faint, marginBottom: 6 }}>
                        Chance of a ¼ inch
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(${ds.length}, 1fr)`,
                                    columnGap: 10, alignItems: "end" }}>
                        {/* the headline number per day */}
                        {ds.map((d) => (
                          <div key={`c${d.date}`} style={{ ...uiNum, fontSize: 15, fontWeight: 800, textAlign: "center",
                                                           paddingBottom: 4,
                                                           color: chance(d) >= 40 ? RAIN : chance(d) >= 20 ? T.body : T.faint }}>
                            {chance(d)}%
                          </div>
                        ))}
                        {/* bar = that same number, so the picture and the figure agree */}
                        {ds.map((d) => {
                          const h = Math.round((Math.min(100, chance(d)) / 100) * PLOT);
                          return (
                            <div key={`b${d.date}`} style={{ height: PLOT, position: "relative" }}
                              title={[`${chance(d)}% chance of 0.25"`,
                                      d.popPct != null ? `${d.popPct}% chance of any rain` : "",
                                      `NBM ${d.inches.toFixed(2)}"`,
                                      d.hiIn != null ? `wet case ${d.hiIn.toFixed(2)}"` : "",
                                      d.omInches != null ? `GFS ${d.omInches.toFixed(2)}"` : "",
                                      d.ecInches != null ? `ECMWF ${d.ecInches.toFixed(2)}"` : ""].filter(Boolean).join(" · ")}>
                              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0,
                                            height: Math.max(chance(d) > 0 ? 2 : 0, h),
                                            background: chance(d) >= 40 ? RAIN : RAIN_TRACK,
                                            borderRadius: "3px 3px 0 0" }} />
                            </div>
                          );
                        })}
                        <div style={{ gridColumn: "1 / -1", height: 1, background: GRID }} />
                        {ds.map((d) => (
                          <div key={`d${d.date}`} style={{ fontSize: 11.5, fontWeight: 800, color: T.ink,
                                                           textAlign: "center", paddingTop: 6 }}>
                            {new Date(`${d.date}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}
                          </div>
                        ))}
                        {/* amount as text, secondary — the part nobody can pin down */}
                        {ds.map((d) => (
                          <div key={`a${d.date}`} style={{ ...uiNum, fontSize: 10.5, color: T.muted,
                                                           textAlign: "center", paddingTop: 2 }}>
                            {amountText(d)}
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 10.5, color: T.faint, textAlign: "center", marginTop: 6 }}>
                        grey figures are the amount NBM expects
                      </div>
                    </div>
                  );
                })()}
                {!w || w.error || !w.hourly ? (
                  <div style={{ fontSize: 12.5, color: T.muted, marginTop: 8 }}>
                    {work ? "Hourly forecast unavailable for this spot right now." : "Reading the hourly forecast…"}
                  </div>
                ) : (() => {
                  const H = w.hourly;
                  const picked = pickHr && pickHr.li === li && H.t[pickHr.i] != null ? pickHr : null;
                  const byDate = new Map();
                  H.t.forEach((t, i) => {
                    const d = String(t).slice(0, 10);
                    if (!byDate.has(d)) byDate.set(d, []);
                    byDate.get(d).push(i);
                  });
                  const rows = [...byDate.entries()];
                  return (
                    <>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap",
                                    gap: 10, flexWrap: "wrap", marginTop: 6 }}>
                        <div style={{ fontSize: 11.5, color: T.muted, lineHeight: 1.5, flex: "1 1 240px" }}>
                          {/* The SOURCE, named on the section. The day outlook above is
                              NBM and these hours are Open-Meteo — two independent
                              models — so they can and do disagree. Leaving that in the
                              small print made a normal disagreement read as a bug. */}
                          <b style={{ color: T.body }}>Hour by hour</b> — GFS. Tap an hour for its numbers.
                        </div>
                        <button onClick={() => setShowWind((v) => !v)}
                          title={showWind ? "Hide the wind strip and show rain only" : "Show the wind strip under each day"}
                          style={{ cursor: "pointer", fontFamily: "inherit", borderRadius: 8, padding: "5px 11px",
                                   fontSize: 11.5, fontWeight: 800, flexShrink: 0,
                                   border: `1px solid ${showWind ? T.inkSoft : T.lineStrong}`,
                                   background: showWind ? T.inkSoft : T.surface,
                                   color: showWind ? "#fff" : T.body }}>
                          {showWind ? "Wind ✓" : "Wind"}
                        </button>
                      </div>

                      <div style={{ overflowX: "auto", marginTop: 12 }}>
                        <div style={{ minWidth: 560 }}>
                          {/* Hour ruler, every 6 hours — enough to place a block
                              without turning the header into a wall of numbers. */}
                          <div style={{ display: "flex", marginLeft: 74, marginBottom: 3 }}>
                            {Array.from({ length: 24 }, (_, h) => (
                              <div key={h} style={{ flex: 1, fontSize: 9.5, color: T.faint, textAlign: "left" }}>
                                {h % 6 === 0 ? (h === 0 ? "12a" : h === 12 ? "12p" : h > 12 ? `${h - 12}p` : `${h}a`) : ""}
                              </div>
                            ))}
                          </div>
                          {rows.map(([date, idxs]) => {
                            // Index by clock hour so a partial first day still lines
                            // up with the ruler instead of sliding left.
                            const slot = new Array(24).fill(null);
                            for (const i of idxs) slot[Number(String(H.t[i]).slice(11, 13))] = i;
                            return (
                              <div key={date} style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 5 }}>
                                <div style={{ width: 74, fontSize: 11, fontWeight: 700, color: T.ink, flexShrink: 0 }}>
                                  {fmtDay(date)}
                                </div>
                                {/* Cells are BUTTONS, and both strips for an hour
                                    select the same hour. They were plain divs with a
                                    `title`, which is a hover tooltip — it does nothing
                                    on a tap, so the caption promised an interaction
                                    that did not exist on the device most likely to be
                                    holding this page. title is kept for desktop hover
                                    as a bonus, not as the mechanism. */}
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: "flex", gap: 1 }}>
                                    {slot.map((i, h) => (
                                      <button key={`r${h}`} disabled={i == null}
                                        onClick={() => setPickHr({ li, i })}
                                        title={i == null ? "" :
                                          `${fmtDay(date)} ${fmtHour(H.t[i])} · rain ${H.precip[i].toFixed(2)}"`
                                          + `${H.pop[i] != null ? ` · ${H.pop[i]}% chance` : ""}`}
                                        style={{ flex: 1, height: 13, minWidth: 0, padding: 0, borderRadius: 2,
                                                 cursor: i == null ? "default" : "pointer",
                                                 background: i == null ? "transparent" : rainCell(H.precip[i]),
                                                 border: `1px solid ${i == null ? "transparent"
                                                   : (picked && picked.i === i ? T.ink : T.lineSoft)}`,
                                                 boxShadow: picked && picked.i === i ? `0 0 0 1px ${T.ink}` : "none" }} />
                                    ))}
                                  </div>
                                  {showWind && (
                                    <div style={{ display: "flex", gap: 1, marginTop: 1 }}>
                                      {slot.map((i, h) => (
                                        <button key={`w${h}`} disabled={i == null}
                                          onClick={() => setPickHr({ li, i })}
                                          title={i == null ? "" :
                                            `${fmtDay(date)} ${fmtHour(H.t[i])} · wind ${H.wind[i]} mph`
                                            + `${H.gust[i] != null ? ` gust ${H.gust[i]}` : ""}`
                                            + `${H.temp[i] != null ? ` · ${H.temp[i]}°F` : ""}`}
                                          style={{ flex: 1, height: 9, minWidth: 0, padding: 0, borderRadius: 2,
                                                   cursor: i == null ? "default" : "pointer",
                                                   background: i == null ? "transparent" : windCell(H.wind[i]),
                                                   border: `1px solid ${i == null ? "transparent"
                                                     : (picked && picked.i === i ? T.ink : T.lineSoft)}`,
                                                   boxShadow: picked && picked.i === i ? `0 0 0 1px ${T.ink}` : "none" }} />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* THE TAPPED HOUR. This is what "tap any hour" now produces —
                          previously the only readout was a `title` tooltip, which is
                          hover-only and therefore invisible on a phone. */}
                      {picked && (
                        <div style={{ marginTop: 10, padding: "9px 12px", borderRadius: 9,
                                      background: T.surfaceAlt, border: `1px solid ${T.lineStrong}`,
                                      display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12.5, fontWeight: 800, color: T.ink }}>
                            {fmtDay(H.t[picked.i])} {fmtHour(H.t[picked.i])}
                          </span>
                          <span style={{ ...uiNum, fontSize: 12.5 }}>
                            <span style={{ color: T.faint }}>rain </span>
                            <b style={{ color: H.precip[picked.i] >= 0.01 ? RAIN : T.faint }}>
                              {H.precip[picked.i].toFixed(2)}"
                            </b>
                          </span>
                          {H.pop[picked.i] != null && (
                            <span style={{ ...uiNum, fontSize: 12.5 }}>
                              <span style={{ color: T.faint }}>chance </span><b style={{ color: T.body }}>{H.pop[picked.i]}%</b>
                            </span>
                          )}
                          {H.wind[picked.i] != null && (
                            <span style={{ ...uiNum, fontSize: 12.5 }}>
                              <span style={{ color: T.faint }}>wind </span><b style={{ color: T.body }}>{H.wind[picked.i]}</b>
                              {H.gust[picked.i] != null && <span style={{ color: T.faint }}> gust {H.gust[picked.i]}</span>}
                              <span style={{ color: T.faint }}> mph</span>
                            </span>
                          )}
                          {H.temp[picked.i] != null && (
                            <span style={{ ...uiNum, fontSize: 12.5 }}>
                              <b style={{ color: T.body }}>{H.temp[picked.i]}°F</b>
                            </span>
                          )}
                          <button onClick={() => setPickHr(null)}
                            style={{ marginLeft: "auto", cursor: "pointer", fontFamily: "inherit", border: "none",
                                     background: "transparent", fontSize: 11, fontWeight: 700, color: T.faint }}>
                            clear
                          </button>
                        </div>
                      )}

                      {/* Keys. Two measures, two scales, stated separately so
                          neither can be read off the other's ramp. */}
                      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 10, fontSize: 10.5, color: T.faint }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          rain/hr
                          {[["0.01", "#cfe0ee"], ["0.02", "#9dc0dd"], ["0.05", "#6b9fcb"], ["0.10", "#3d7fb4"], ["0.20+", RAIN]].map(([lab, c]) => (
                            <span key={lab} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                              <span style={{ width: 9, height: 9, borderRadius: 2, background: c, display: "inline-block" }} />{lab}"
                            </span>
                          ))}
                        </span>
                        {showWind && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            wind
                            {[["5", "#e8ece6"], ["10", "#c3d0bd"], ["15", "#9bb192"], ["20", "#74926a"], ["20+", "#4d6d45"]].map(([lab, c]) => (
                              <span key={lab} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                                <span style={{ width: 9, height: 9, borderRadius: 2, background: c, display: "inline-block" }} />{lab}
                              </span>
                            ))}
                            <span>mph</span>
                          </span>
                        )}
                      </div>

                      {/* Counts and ranges. No rating column — the numbers are the
                          output, and what they add up to is the reader's call. */}
                      <div style={{ overflowX: "auto", marginTop: 16 }}>
                        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 440 }}>
                          <thead>
                            <tr>
                              {["Day", "Rain", "Chance", "Hours w/ rain", "Hi / Lo", "Wind", "Gust"].map((h, hi) => (
                                <th key={h} style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".06em",
                                                     textTransform: "uppercase", color: T.faint,
                                                     textAlign: hi === 0 ? "left" : "right", padding: "0 8px 6px" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {w.days.map((d) => (
                              <tr key={d.date} style={{ borderTop: `1px solid ${T.lineSoft}` }}>
                                <td style={{ padding: "8px", fontSize: 12.5, fontWeight: 700, color: T.ink, whiteSpace: "nowrap" }}>
                                  {fmtDay(d.date)}
                                </td>
                                <td style={{ ...uiNum, padding: "8px", fontSize: 12.5, textAlign: "right",
                                             color: d.precipIn >= 0.01 ? RAIN : T.faint }}>
                                  {d.precipIn >= 0.01 ? `${d.precipIn.toFixed(2)}"` : "—"}
                                </td>
                                <td style={{ ...uiNum, padding: "8px", fontSize: 12, textAlign: "right", color: T.muted }}>
                                  {d.popPct == null ? "—" : `${d.popPct}%`}
                                </td>
                                {/* WHOLE-DAY count. It used to be daylight-only, which
                                    printed "0.40"" beside "0 of 13" on the same row
                                    when the rain fell at 6am. */}
                                <td style={{ ...uiNum, padding: "8px", fontSize: 12, textAlign: "right", color: T.body }}
                                    title={`Hours in the day with at least ${w.measurableIn}" forecast`}>
                                  {d.rainHours != null ? `${d.rainHours} of ${d.hoursCovered}`
                                    : `${d.daylightHours - d.dryDaylightHours} of ${d.daylightHours}`}
                                </td>
                                <td style={{ ...uiNum, padding: "8px", fontSize: 12, textAlign: "right" }}>
                                  <span style={{ color: T.ink, fontWeight: 700 }}>{d.hiF == null ? "—" : `${d.hiF}°`}</span>
                                  <span style={{ color: T.faint }}> / {d.loF == null ? "—" : `${d.loF}°`}</span>
                                </td>
                                <td style={{ ...uiNum, padding: "8px", fontSize: 12, textAlign: "right", color: T.body }}>
                                  {d.minWindMph == null ? "—" : `${d.minWindMph}–${d.maxWindMph}`}
                                </td>
                                <td style={{ ...uiNum, padding: "8px", fontSize: 12, textAlign: "right", color: T.muted }}>
                                  {d.maxGustMph ? d.maxGustMph : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          );
        })()
      ) : tab === "alerts" ? (
        /* ── ALERTS ────────────────────────────────────────────────────────
           The only surface here that reaches out rather than waiting to be
           opened. Every alert is the USER'S number — kind, threshold, window,
           location — and the email reports what was measured against it and
           nothing else. Same reason the Hours tab stopped rating days: the
           judgement is theirs, the measurement is ours.

           Each row states its rule in words ("at least 0.5" forecast in the
           next 24 hours") because a table of kind/threshold/window columns
           makes you reassemble the sentence yourself every time you read it. */
        (() => {
          const myEmail = getCurrentUserEmail() || "";
          const openForm = () => setAlertForm({
            kind: "rain_forecast", threshold: 0.5, window_hours: 24,
            location_id: "", email: myEmail, cooldown_hours: 24,
          });
          const save = () => {
            const f = alertForm;
            const k = alertKind(f.kind);
            // Same `Number("") === 0` trap the map picker had, and it bites harder
            // here: clearing the box saved a threshold of ZERO, which for a rain
            // alert is "tell me when at least 0 inches are forecast" — it fires
            // every cycle, forever, at whatever address is on the row.
            const thr = coord(f.threshold);
            // Alerts go only to the signed-in user's own login email; the server
            // ignores anything else, so the form no longer offers a free-text box.
            if (!myEmail.trim()) { setErr("Your login has no email address, so an alert has nowhere to go."); return; }
            if (!Number.isFinite(thr)) { setErr("An alert needs a threshold."); return; }
            sbInsert("weather_alerts", [{
              kind: f.kind,
              threshold: thr,
              window_hours: k && k.window ? Number(f.window_hours) : null,
              location_id: f.location_id || null,
              email: myEmail.trim(),
              cooldown_hours: Number(f.cooldown_hours) || 24,
              active: true,
            }])
              .then(() => { setAlertForm(null); setErr(""); loadAlerts(); })
              // Surface WHAT failed. "Could not save that alert." sent us looking at
              // the evaluator when the cause was a missing column default two layers
              // down — a generic failure string costs more than it protects.
              .catch((e) => setErr(`Could not save that alert — ${String((e && e.message) || e).slice(0, 200)}`));
          };
          // PATCH, not upsert. An upsert of {id, active} is still an INSERT to
          // Postgres, so the NOT NULL columns it omits (kind, email) would fail
          // before ON CONFLICT was ever reached. PATCH is the correct verb for
          // changing one field, and it is what the rest of the app uses.
          const toggle = (a) => {
            authedFetch(`${SUPABASE_URL}/rest/v1/weather_alerts?id=eq.${a.id}`, {
              method: "PATCH",
              headers: { Prefer: "return=minimal" },
              body: JSON.stringify({ active: !a.active }),
            })
              .then((r) => { if (!r.ok) throw new Error(`${r.status}`); setErr(""); loadAlerts(); })
              .catch((e) => setErr(`Could not change that alert — ${String((e && e.message) || e).slice(0, 120)}`));
          };
          const remove = (a) => {
            sbDelete("weather_alerts", `id=eq.${a.id}`)
              .then(loadAlerts)
              .catch(() => setErr("Could not remove that alert."));
          };
          const locName = (id) => {
            if (!id) return "all my locations";
            const l = locs.find((x) => x.id === id);
            return l ? l.name : "a removed location";
          };
          return (
            <>
              <div style={{ ...card }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: T.ink }}>Email me when…</div>
                    <div style={{ fontSize: 11.5, color: T.muted, marginTop: 3, lineHeight: 1.5, maxWidth: 560 }}>
                      You pick the number; we only report what was measured against it. Each alert fires
                      <b> once per event</b> — a new day or a new reading — not once per check.
                    </div>
                  </div>
                  {!alertForm && (
                    <button onClick={openForm}
                      style={{ cursor: "pointer", fontFamily: "inherit", border: "none", borderRadius: 8,
                               padding: "8px 14px", fontSize: 12.5, fontWeight: 800, background: T.green, color: "#fff" }}>
                      + Add an alert
                    </button>
                  )}
                </div>

                {alertForm && (() => {
                  const k = alertKind(alertForm.kind);
                  return (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.lineSoft}` }}>
                      <div style={{ display: "grid", gap: 10,
                                    gridTemplateColumns: isMobile ? "1fr" : "minmax(190px,1.2fr) minmax(150px,1fr) minmax(150px,1fr)" }}>
                        <label style={{ display: "block" }}>
                          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint }}>Tell me about</span>
                          <select value={alertForm.kind}
                            onChange={(e) => {
                              const nk = alertKind(e.target.value);
                              setAlertForm((f) => ({ ...f, kind: e.target.value,
                                threshold: nk ? nk.def : f.threshold,
                                window_hours: nk && nk.window ? nk.windowDef : null }));
                            }}
                            style={{ width: "100%", fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "7px 8px",
                                     borderRadius: 7, border: `1px solid ${T.lineStrong}`, background: T.surface, color: T.ink, cursor: "pointer", boxSizing: "border-box" }}>
                            {ALERT_KINDS.map((x) => <option key={x.kind} value={x.kind}>{x.label}</option>)}
                          </select>
                        </label>
                        <label style={{ display: "block" }}>
                          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint }}>
                            Threshold ({k ? k.unit : ""})
                          </span>
                          <input type="number" step="any" value={alertForm.threshold}
                            onChange={(e) => setAlertForm((f) => ({ ...f, threshold: e.target.value }))}
                            style={{ width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: 13, padding: "7px 8px",
                                     borderRadius: 7, border: `1px solid ${T.lineStrong}` }} />
                        </label>
                        {k && k.window ? (
                          <label style={{ display: "block" }}>
                            <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint }}>
                              {alertForm.kind === "calm" ? "For how many hours" : "Over"}
                            </span>
                            <select value={alertForm.window_hours}
                              onChange={(e) => setAlertForm((f) => ({ ...f, window_hours: Number(e.target.value) }))}
                              style={{ width: "100%", fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "7px 8px",
                                       borderRadius: 7, border: `1px solid ${T.lineStrong}`, background: T.surface, color: T.ink, cursor: "pointer", boxSizing: "border-box" }}>
                              {k.window.map((w) => (
                                <option key={w} value={w}>
                                  {alertForm.kind === "calm" ? `${w} hours` : w === 168 ? "7 days" : `${w} hours`}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : <div />}
                        <label style={{ display: "block" }}>
                          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint }}>Where</span>
                          <select value={alertForm.location_id}
                            onChange={(e) => setAlertForm((f) => ({ ...f, location_id: e.target.value }))}
                            style={{ width: "100%", fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "7px 8px",
                                     borderRadius: 7, border: `1px solid ${T.lineStrong}`, background: T.surface, color: T.ink, cursor: "pointer", boxSizing: "border-box" }}>
                            {/* Blank = every location, and it keeps covering new ones as they are added. */}
                            <option value="">All my locations</option>
                            {locs.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                          </select>
                        </label>
                        <label style={{ display: "block" }}>
                          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint }}>Send to</span>
                          <div style={{ width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: 13, padding: "7px 8px",
                                        borderRadius: 7, border: `1px solid ${T.line}`, background: T.surfaceAlt, color: T.ink,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title="Alerts go to the email you sign in with.">
                            {myEmail || "No email on your login"}
                          </div>
                        </label>
                        <label style={{ display: "block" }}>
                          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint }}
                            title="The minimum gap between emails from this alert, on top of the once-per-event rule.">
                            At most once every
                          </span>
                          <select value={alertForm.cooldown_hours}
                            onChange={(e) => setAlertForm((f) => ({ ...f, cooldown_hours: Number(e.target.value) }))}
                            style={{ width: "100%", fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "7px 8px",
                                     borderRadius: 7, border: `1px solid ${T.lineStrong}`, background: T.surface, color: T.ink, cursor: "pointer", boxSizing: "border-box" }}>
                            {[3, 6, 12, 24, 48].map((h) => <option key={h} value={h}>{h} hours</option>)}
                          </select>
                        </label>
                      </div>
                      {/* The rule in plain words, as configured, before saving. */}
                      {k && (
                        <div style={{ fontSize: 12, color: T.body, marginTop: 12, lineHeight: 1.5 }}>
                          <b>{locName(alertForm.location_id)}</b> — email {alertForm.email || "…"} when{" "}
                          {k.how(alertForm.threshold, alertForm.window_hours)}.
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button onClick={save}
                          style={{ cursor: "pointer", fontFamily: "inherit", border: "none", borderRadius: 7,
                                   padding: "8px 15px", fontSize: 12.5, fontWeight: 800, background: T.green, color: "#fff" }}>
                          Save alert
                        </button>
                        <button onClick={() => { setAlertForm(null); setErr(""); }}
                          style={{ cursor: "pointer", fontFamily: "inherit", border: `1px solid ${T.lineStrong}`, borderRadius: 7,
                                   padding: "7px 12px", fontSize: 12.5, fontWeight: 700, background: T.surface, color: T.body }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* The list. Each row reads as the sentence it is. */}
              <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                {alerts === null ? (
                  <div style={{ ...card, fontSize: 12.5, color: T.muted }}>Loading your alerts…</div>
                ) : !alerts.length ? (
                  <div style={{ ...card, fontSize: 12.5, color: T.body }}>
                    No alerts yet. Rain forecast and rain received are the two most people start with —
                    a half inch coming, or a half inch already down.
                  </div>
                ) : alerts.map((a) => {
                  const k = alertKind(a.kind);
                  return (
                    <div key={a.id} style={{ ...card, padding: isMobile ? 11 : 13, opacity: a.active ? 1 : 0.55,
                                             display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: T.ink }}>
                          {k ? k.label : a.kind}
                          <span style={{ fontWeight: 600, color: T.muted }}> · {locName(a.location_id)}</span>
                        </div>
                        <div style={{ fontSize: 11.5, color: T.body, marginTop: 3, lineHeight: 1.45 }}>
                          When {k ? k.how(a.threshold, a.window_hours) : `${a.threshold}`}.
                        </div>
                        <div style={{ fontSize: 10.5, color: T.faint, marginTop: 3 }}>
                          → {myEmail || a.email} · at most once every {a.cooldown_hours}h
                          {a.last_fired_at && <> · last sent {new Date(a.last_fired_at).toLocaleString("en-US",
                            { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })}</>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                        <button onClick={() => toggle(a)} title={a.active ? "Pause this alert" : "Turn this alert back on"}
                          style={{ cursor: "pointer", fontFamily: "inherit", borderRadius: 7, padding: "5px 11px",
                                   fontSize: 11.5, fontWeight: 800,
                                   border: `1px solid ${a.active ? T.inkSoft : T.lineStrong}`,
                                   background: a.active ? T.inkSoft : T.surface, color: a.active ? "#fff" : T.body }}>
                          {a.active ? "On" : "Off"}
                        </button>
                        <button onClick={() => remove(a)} title="Delete this alert"
                          style={{ cursor: "pointer", fontFamily: "inherit", border: `1px solid ${T.lineStrong}`, borderRadius: 7,
                                   padding: "5px 10px", fontSize: 11.5, fontWeight: 700, background: T.surface, color: T.faint }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ fontSize: 10.5, color: T.faint, marginTop: 14, lineHeight: 1.5, maxWidth: 720 }}>
                Received alerts read gauge-corrected radar topped up from the live radar edge; forecast alerts read
                NBM, and the hourly ones (frost, heat, wind, calm, dry stretch) read Open-Meteo. Rain-arriving is
                checked hourly; everything else three times a day, so an alert can lag its trigger by that much.
              </div>
            </>
          );
        })()
      ) : (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
            <thead>
              {/* Two header rows so it is unmistakable which side is MEASURED and
                  which is a forecast. They were reading as one continuous row of
                  numbers, which invites comparing a gauge total against a
                  forecast day as if they were the same kind of thing. */}
              <tr>
                <th style={{ ...th, paddingLeft: 16, paddingBottom: 2 }} />
                <th colSpan={4} style={{ ...th, paddingBottom: 2, textAlign: "center", color: "#1f5b8f",
                                         borderRight: `1px solid ${T.lineStrong}`, background: "#f6fafd" }}>
                  Received · gauge-corrected radar
                </th>
                <th colSpan={dates.length} style={{ ...th, paddingBottom: 2, textAlign: "center" }}>
                  Forecast
                </th>
                <th style={{ ...th }} />
              </tr>
              <tr>
                <th style={{ ...th, paddingLeft: 16 }}>Location</th>
                <th style={{ ...th, textAlign: "right", background: "#f6fafd" }}>Today</th>
                <th style={{ ...th, textAlign: "right", background: "#f6fafd" }}>24h</th>
                <th style={{ ...th, textAlign: "right", background: "#f6fafd" }}>72h</th>
                <th style={{ ...th, textAlign: "right", background: "#f6fafd",
                             borderRight: `1px solid ${T.lineStrong}`, paddingRight: 14 }}>7 day</th>
                {dates.map((d) => {
                  const { wd, md } = dayLabel(d);
                  return (
                    <th key={d.date} style={{ ...th, textAlign: "center" }}>
                      {wd} <span style={{ color: T.lineStrong }}>{md}</span>
                      {d.partial && <span style={{ fontWeight: 600 }}> ·{d.hoursCovered}h</span>}
                    </th>
                  );
                })}
                <th style={{ ...th }} />
              </tr>
            </thead>
            <tbody>
              {locs.map((l, i) => {
                const g = gauge && gauge.locations ? gauge.locations[i] : null;
                const f = fc && fc.locations ? fc.locations[i] : null;
                return (
                  <tr key={l.id} style={{ borderTop: `1px solid ${T.lineSoft}` }}>
                    <td style={{ padding: "10px 8px 10px 16px", fontSize: 13, fontWeight: 700, color: T.ink, whiteSpace: "nowrap" }}>
                      {l.name}
                      <div style={{ ...uiNum, fontSize: 10.5, color: T.faint, fontWeight: 500 }}>
                        {Number(l.lat).toFixed(3)}, {Number(l.lon).toFixed(3)}
                      </div>
                    </td>
                    {/* Today leads — it is the number being checked against a
                        window or a rain gauge on the wall. The rest give it context. */}
                    <td style={{ ...uiNum, padding: "10px 8px", fontSize: 17, fontWeight: 800, textAlign: "right",
                                 background: "#f6fafd", color: g && g.today > 0 ? "#1f5b8f" : T.faint }}>
                      {g ? (g.today == null ? "—" : `${g.today.toFixed(2)}"`) : "…"}
                    </td>
                    <td style={{ ...uiNum, padding: "10px 8px", fontSize: 13, textAlign: "right",
                                 background: "#f6fafd", color: T.body }}>
                      {g ? (g.h24 == null ? "—" : `${g.h24.toFixed(2)}"`) : "…"}
                    </td>
                    <td style={{ ...uiNum, padding: "10px 8px", fontSize: 13, textAlign: "right",
                                 background: "#f6fafd", color: T.body }}>
                      {g ? (g.h72 == null ? "—" : `${g.h72.toFixed(2)}"`) : "…"}
                    </td>
                    <td style={{ ...uiNum, padding: "10px 14px 10px 8px", fontSize: 13, textAlign: "right",
                                 background: "#f6fafd", color: T.body, borderRight: `1px solid ${T.lineStrong}` }}>
                      {g ? (g.d7 == null ? "—" : `${g.d7.toFixed(2)}"`) : "…"}
                    </td>
                    {dates.map((_, di) => {
                      const d = f && f.days ? f.days[di] : null;
                      if (!d) return <td key={di} style={{ textAlign: "center", color: T.faint }}>…</td>;
                      const wet = d.popPct != null && d.popPct >= 50;
                      return (
                        // Amount leads, chance supports. "How much" is the number
                        // being planned against; the probability qualifies it.
                        <td key={d.date} style={{ padding: "8px 6px", textAlign: "center", background: wet ? "#f4f9fd" : "transparent", opacity: d.partial ? 0.72 : 1 }}>
                          <div style={{ ...uiNum, fontSize: 16, fontWeight: 800, lineHeight: 1.15,
                                        color: d.inches >= 0.10 ? "#1f5b8f" : d.inches >= 0.01 ? T.ink : T.faint }}>
                            {amtText(d)}
                          </div>
                          <div style={{ ...uiNum, fontSize: 10.5, color: T.muted }}>
                            {d.popPct == null ? "—" : `${d.popPct}%`}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ padding: "8px 12px 8px 4px", textAlign: "right" }}>
                      <button onClick={() => delLoc(l.id)} title={`Remove ${l.name}`}
                        style={{ cursor: "pointer", fontFamily: "inherit", border: `1px solid ${T.lineStrong}`, borderRadius: 6,
                                 padding: "3px 8px", fontSize: 10.5, fontWeight: 700, background: T.surface, color: T.body }}>
                        remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {busy && <div style={{ fontSize: 11, color: T.faint, marginTop: 8 }}>reading the forecast models — usually 10–20 seconds…</div>}

      {/* FIRST RUN. With nothing saved, every tab except Radar is hidden and the
          "no locations yet" line lives in a branch the default tab never reaches —
          so a farmer opening this for the first time got a map, a small button
          underneath, and no indication that the page is built around saved places.
          The prompt has to be the loud thing on the screen until there is one. */}
      {locs && locs.length === 0 && !adding && (
        <div style={{ ...card, marginTop: 14, textAlign: "center", padding: isMobile ? 18 : 26 }}>
          <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 800, color: T.ink }}>
            Add a field to get started
          </div>
          <div style={{ fontSize: 12.5, color: T.muted, marginTop: 6, lineHeight: 1.55,
                        maxWidth: 460, margin: "6px auto 0" }}>
            {mySpots && mySpots.length
              ? "Start with your own ground — we’ll track what actually fell there, gauge-corrected radar to the minute, alongside what’s forecast."
              : "Pick a spot on the map and we’ll track what actually fell there — gauge-corrected radar, to the minute — alongside what’s forecast. Add as many as you like."}
          </div>
          {mySpots && mySpots.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 14 }}>
              {mySpots.map((sp) => (
                <button key={sp.county} disabled={spotBusy} onClick={() => addSpots([sp])}
                  style={{ cursor: spotBusy ? "default" : "pointer", fontFamily: "inherit", borderRadius: 10, padding: "9px 14px",
                           border: `1px solid ${T.lineStrong}`, background: T.surface, color: T.ink, textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>+ {sp.name}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    {sp.nFields} field{sp.nFields === 1 ? "" : "s"}{sp.acres ? ` · ${sp.acres.toLocaleString()} ac` : ""}
                  </div>
                </button>
              ))}
              {mySpots.length > 1 && (
                <button disabled={spotBusy} onClick={() => addSpots(mySpots)}
                  style={{ cursor: spotBusy ? "default" : "pointer", fontFamily: "inherit", border: "none", borderRadius: 10,
                           padding: "9px 16px", fontSize: 13, fontWeight: 800, background: T.green, color: "#fff" }}>
                  Add all {mySpots.length}
                </button>
              )}
            </div>
          )}
          <button onClick={() => setAdding(true)}
            style={{ cursor: "pointer", fontFamily: "inherit", border: "none", borderRadius: 9,
                     padding: "10px 20px", fontSize: 13.5, fontWeight: 800, background: T.green,
                     color: "#fff", marginTop: 14,
                     ...(mySpots && mySpots.length ? { background: "transparent", color: T.green, fontSize: 12.5, padding: "6px 10px" } : {}) }}>
            {mySpots && mySpots.length ? "or pick any spot on the map" : "+ Add a location"}
          </button>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {/* Suppressed while the first-run prompt above is doing this job, so the two
            do not stack up saying the same thing. */}
        {!adding ? (locs && locs.length === 0 ? null : (
          <button onClick={() => setAdding(true)}
            style={{ cursor: "pointer", fontFamily: "inherit", border: `1px solid ${T.lineStrong}`, borderRadius: 8,
                     padding: "7px 14px", fontSize: 12.5, fontWeight: 700, background: T.surface, color: T.inkSoft }}>
            + Add a location
          </button>)
        ) : (
          <div style={{ ...card }}>
            <div style={{ display: "grid", gridTemplateColumns: (isMobile || isPortal) ? "1fr" : "minmax(240px,1fr) minmax(280px,1.1fr)", gap: 14 }}>
              {/* Route 1: pick a customer — AGENT ONLY. It lists customer names, which
                  is book data and none of a grower's business, so the portal gets the
                  map alone (full width there, since it is the only route). */}
              {!isPortal && (
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint, marginBottom: 5 }}>
                  Pick a farm
                </div>
                <input value={farmQuery} onChange={(e) => setFarmQuery(e.target.value)}
                  placeholder={farmOpts ? `Search ${farmOpts.length} farms…` : "Loading farms…"}
                  style={{ fontFamily: "inherit", fontSize: 13, padding: "7px 9px", borderRadius: 7, border: `1px solid ${T.lineStrong}`, width: "100%", boxSizing: "border-box" }} />
                <div style={{ maxHeight: 132, overflowY: "auto", border: `1px solid ${T.lineSoft}`, borderRadius: 7, marginTop: 6 }}>
                  {(farmOpts || [])
                    .filter((f) => !farmQuery.trim() || (f.name || "").toLowerCase().includes(farmQuery.trim().toLowerCase()))
                    .slice(0, 40)
                    .map((f) => (
                      <button key={f.id}
                        onClick={() => setForm({ name: f.name || "", lat: Number(f.lat).toFixed(4), lon: Number(f.lon).toFixed(4) })}
                        style={{ display: "block", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                                 border: "none", borderBottom: `1px solid ${T.lineSoft}`, padding: "6px 9px", fontSize: 12,
                                 background: form.name === f.name ? "#eef4ee" : T.surface, color: T.ink }}>
                        {f.name}
                      </button>
                    ))}
                  {farmOpts && !farmOpts.length && (
                    <div style={{ padding: "8px 9px", fontSize: 11.5, color: T.faint }}>No farms have coordinates on file.</div>
                  )}
                </div>
              </div>
              )}

              <div>
                {/* Route 2: anywhere else. Click drops the pin. */}
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: T.faint, marginBottom: 5 }}>
                  {isPortal ? "Tap the map to pick a spot" : "Or tap the map"}
                </div>
                <div ref={pickMapEl}
                  style={{ height: isPortal ? 240 : 172, borderRadius: 8, overflow: "hidden", border: `1px solid ${T.line}`, background: "#dde6dd" }} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name it"
                style={{ fontFamily: "inherit", fontSize: 13, padding: "7px 9px", borderRadius: 7, border: `1px solid ${T.lineStrong}`, minWidth: 190 }} />
              <span style={{ ...uiNum, fontSize: 11.5, color: form.lat && form.lon ? T.body : T.faint }}>
                {form.lat && form.lon ? `${form.lat}, ${form.lon}` : "no point chosen yet"}
              </span>
              <button onClick={addLoc} disabled={!form.name.trim() || !form.lat || !form.lon}
                style={{ cursor: (!form.name.trim() || !form.lat || !form.lon) ? "default" : "pointer", fontFamily: "inherit",
                         border: "none", borderRadius: 7, padding: "8px 15px", fontSize: 12.5, fontWeight: 800,
                         background: (!form.name.trim() || !form.lat || !form.lon) ? T.lineStrong : T.green, color: "#fff",
                         marginLeft: "auto" }}>Save</button>
              <button onClick={() => { setAdding(false); setErr(""); setForm({ name: "", lat: "", lon: "" }); setFarmQuery(""); }}
                style={{ cursor: "pointer", fontFamily: "inherit", border: `1px solid ${T.lineStrong}`, borderRadius: 7,
                         padding: "7px 12px", fontSize: 12.5, fontWeight: 700, background: T.surface, color: T.body }}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize: 10.5, color: T.faint, marginTop: 16, lineHeight: 1.5, maxWidth: 760 }}>
        Chance is the calibrated probability of measurable rain (over 0.01"). The amount beside it is the
        blend average for that day, <b>not</b> the amount if it rains — so a 40% day can average a trace.
        That is what the <b>wet case</b> cap is for: the same day at the 90th percentile.
        Received totals are gauge-corrected radar through the time above, topped up to the minute
        from radar alone — so a storm falling right now is in the number, flagged as not yet gauge-corrected.
      </div>
    </div>
  );
}

// ── The empty state every JD-backed portal screen shares ─────────────────────
// A grower who has never connected sees a page with nothing on it otherwise,
// which reads as broken rather than as "there is one step left" — so the page
// says which it is, and puts the fix on it.
//
// TWO DIFFERENT EMPTY PAGES, and the headline has to be the one that's true.
// An already-connected farm that simply hasn't got 3 harvest years behind it was
// being told to connect an account it had connected — the fix was to sync data
// it has already sent. So the panel leads on whichever state it is actually in
// and only offers the connect button to farms that need it. W Cleveland (2
// seasons, connected 2026-08-10) is the case that surfaced this.
//
// The link is minted SERVER-side (fn=jd-myconnect&start=<farmer>) against a farm
// the caller already holds a portal grant on, and the state token it returns
// binds the connection to that farmer_id. So the worst this button can do is
// attach the grower's own John Deere org to their own book row. It never takes a
// farmer_id from the page — the server intersects whatever is asked for with the
// caller's grants and 403s the rest.
function JdConnectPanel({ isMobile = false, what = "field data", connectedTitle = null,
                          connectedNote = null, restrictFarmerIds = null }) {
  const [farms, setFarms] = useState(null);   // null = loading
  const [busy, setBusy]   = useState(null);   // farmer_id being started
  const [err, setErr]     = useState(null);
  // Always asked for BY ID. The handler intersects whatever is named here with
  // the caller's own grants, so naming them changes nothing for a farmer — but
  // an admin in "Preview as" holds no grants, and an unqualified call would ask
  // this endpoint a question about the whole book.
  const ids = useMemo(() => (restrictFarmerIds || []).filter(Boolean).map(encodeURIComponent).join(","),
    [restrictFarmerIds]);

  useEffect(() => {
    if (!ids) { setFarms([]); return; }
    let alive = true;
    authedFetch(`/api/market?fn=jd-myconnect&farmer=${ids}`)
      .then(r => r.json())
      .then(d => { if (alive) { if (d && d.error) setErr(d.error); else setFarms((d && d.farms) || []); } })
      .catch(e => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [ids]);

  const start = async (id) => {
    setBusy(id); setErr(null);
    try {
      const d = await authedFetch(`/api/market?fn=jd-myconnect&farmer=${ids}&start=${encodeURIComponent(id)}`).then(r => r.json());
      if (d && d.url) { window.location.assign(d.url); return; }   // → John Deere's own approval screen
      if (d && d.connected) { window.location.reload(); return; }  // connected in another tab meanwhile
      setErr((d && d.error) || "Couldn't start the connection — please try again.");
    } catch (e) { setErr(String((e && e.message) || e)); }
    finally { setBusy(null); }
  };

  const pending = (farms || []).filter(f => !f.connected);
  const synced  = (farms || []).filter(f => f.connected);
  // Nothing left to connect: every farm on this login is already sending data,
  // so the page is empty for a reason that has nothing to do with connecting.
  const allOn = farms !== null && farms.length > 0 && pending.length === 0;

  return (
    <div style={{ ...uiCard(isMobile ? 16 : 22), maxWidth: 640 }}>
      <div style={{ fontSize: 26, marginBottom: 8 }}>{allOn ? "🌱" : "🚜"}</div>
      <div style={{ fontWeight: 800, color: T.ink, fontSize: isMobile ? 16 : 17, marginBottom: 6 }}>
        {allOn
          ? (connectedTitle || `Not enough ${what} yet`)
          : "Your SILAS account isn’t connected to John Deere Operations Center"}
      </div>
      <div style={{ fontSize: 13, color: T.body, lineHeight: 1.6, marginBottom: 16 }}>
        {allOn
          ? (connectedNote || "Your John Deere connection is live — this page fills in as more seasons come across.")
          : <>Once it is, your planting and harvest records flow in on their own and this page fills
             with your {what} — nothing to upload, nothing to send us. It takes about a minute, and
             you can switch it off any time from Ops Center → Setup → Connections.</>}
      </div>

      {err && <div style={{ fontSize: 12.5, color: T.danger, marginBottom: 12 }}>{err}</div>}
      {farms === null && !err && <div style={{ fontSize: 12.5, color: T.faint }}>Checking your connection…</div>}
      {farms !== null && !farms.length && !err && (
        <div style={{ fontSize: 12.5, color: T.faint }}>
          No farm is linked to your login yet — call us at (585) 233-1066 and we'll sort it out.
        </div>
      )}

      {pending.map(f => (
        <div key={f.farmer_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                                        gap: 12, flexWrap: "wrap", padding: "10px 0", borderTop: `1px solid ${T.lineSoft}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: T.ink, fontSize: 13.5 }}>{f.name || "Your operation"}</div>
            {f.pending && <div style={{ fontSize: 11.5, color: T.faint }}>A connection link was already sent — this opens the same one.</div>}
          </div>
          <button onClick={() => start(f.farmer_id)} disabled={busy === f.farmer_id}
            style={{ background: "#2d5a10", color: "#fff", border: "none", borderRadius: 8, padding: "11px 18px",
                     fontSize: 13, fontWeight: 800, cursor: busy === f.farmer_id ? "default" : "pointer",
                     opacity: busy === f.farmer_id ? 0.6 : 1, fontFamily: "inherit" }}>
            {busy === f.farmer_id ? "Opening John Deere…" : "Connect John Deere"}
          </button>
        </div>
      ))}

      {/* Connected but empty: the connection is fine and the sync just hasn't run
          or hasn't found enough history. Saying "connect" here would be a lie.
          When EVERY farm is in this state the explanation is the lede above, so
          the row is just the standing fact and doesn't repeat it. */}
      {synced.map(f => (
        <div key={f.farmer_id} style={{ padding: "10px 0", borderTop: `1px solid ${T.lineSoft}`, fontSize: 12.5, color: T.body }}>
          <span style={{ color: "#2d6a2d", fontWeight: 800, marginRight: 6 }}>✓</span>
          <b style={{ color: T.ink }}>{f.name || "Your operation"}</b> — connected
          {f.last_sync_at ? `, last updated ${String(f.last_sync_at).slice(0, 10)}` : ", first sync pending"}.
          {/* A policy the grower never pressed a button for still reads as
              connected, because it shares an Ops Center account with one they did.
              Say which, or it looks like we connected something on our own. */}
          {f.via_org && <div style={{ fontSize: 11.5, color: T.faint, marginTop: 2 }}>
            through your <b>{f.via_org}</b> Operations Center account — nothing separate to connect.
          </div>}
          {!allOn && <>{" "}{connectedNote || "Records appear here once John Deere has sent us a season."}</>}
        </div>
      ))}

      {/* Instructions for a screen nobody with a live connection is going to see. */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.lineSoft}`, fontSize: 11.5, color: T.faint, lineHeight: 1.6 }}>
        {allOn
          ? <>Questions about your numbers? Call us at (585) 233-1066.</>
          : <>On the John Deere screen, tick your operation and turn on <b>both</b> Locations &amp; Boundaries
             and Field Operations (it may show as Field Analyzer or Work). Stuck? Call us
             at (585) 233-1066.</>}
      </div>
    </div>
  );
}

// WHEN IS A FARM "DONE"? Two different rules, because the two operations are
// measured differently, and both must match what api/_jd.js hClaimWatch applies
// — these constants exist so a label can never drift from the rule again.
//
// PLANTING is ACRE-based: machine seed-rate coverage against the baseline acres.
// It is set below 100 on purpose — machine coverage never reaches the reported
// acreage (headlands, waterways, GPS gaps, point clouds thinning at the edges),
// so a field that is genuinely finished lands in the low-to-mid 90s. Kennedy's
// 899 of 954 soybean acres is a fully planted farm, not a farm 55 acres short.
//
// HARVEST is FIELD-based: the share of insured fields whose machine coverage
// cleared a headland-tolerant floor. An acre ratio can't legitimately reach 100%
// even when every field has been combined, which is why it counts fields.
const PLANT_DONE   = 0.90;   // hClaimWatch: plant_coverage >= 0.90
const HARVEST_DONE = 0.95;   // hClaimWatch: COMPLETE, over FIELDS not acres

// Last Field Progress (jd-claimwatch) payload per viewer|year|scope, for the life
// of the page only. See the "SHOW THE LAST ANSWER" note inside HarvestTrackerView.
const _claimWatchCache = new Map();

function HarvestTrackerView({ isMobile = false, farmers = [], mode: viewMode = null, restrictFarmerIds = null }) {
  const isPortal = viewMode === "portal";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [connectedIds, setConnectedIds] = useState(() => new Set()); // JD-connected farmer_ids
  const [survey, setSurvey] = useState(null); // { farms, progressByFarmCrop, intentByFarmCrop }
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [mode, setMode] = useState("harvest"); // "harvest" | "planting"
  const [sort, setSort] = useState("pace");     // pace | claims | acres | name
  const [expanded, setExpanded] = useState(() => new Set());
  const [reloadKey, setReloadKey] = useState(0);
  const [cropFilter, setCropFilter] = useState(""); // "" = all crops
  // Corn only: 016 grain vs 026 silage. "" = both. Reset whenever the crop changes, so a
  // silage selection cannot survive onto WHEAT and silently narrow a crop that has no types.
  const [cornType, setCornType] = useState("");
  // "" = every state. A county name is NOT unique across states in this book — ERIE and
  // COLUMBIA are each in NY and PA — so county selections are keyed STATE|NAME and are
  // cleared whenever the state changes, rather than carrying a now-ambiguous name over.
  const [stateFilter, setStateFilter] = useState("");
  const [countySel, setCountySel] = useState(() => new Set());
  const [year, setYear] = useState("");             // "" = latest per farm
  const [savingIntended, setSavingIntended] = useState(null); // `${farmerId}|${crop}`
  // Agent-excluded (farm, crop year, crop) cells — see partitionPaceExcluded in
  // trackedSource.js. AGENCY ONLY: never read, never applied, never rendered on a portal
  // screen, so a grower's own pace is exactly what their machine data says. A missing table
  // (sql/113 not yet applied) reads as nothing excluded.
  const [paceExcluded, setPaceExcluded] = useState(() => new Set());
  const [savingExclusion, setSavingExclusion] = useState(null);
  const [exclusionsKey, setExclusionsKey] = useState(0);
  useEffect(() => {
    if (isPortal) { setPaceExcluded(new Set()); return; }
    let alive = true;
    sbFetch("field_progress_exclusions", { select: "farmer_id,crop_year,crop" })
      .then(rows => { if (alive) setPaceExcluded(new Set((rows || []).map(r => paceExclusionKey(r.farmer_id, r.crop_year, r.crop)))); })
      .catch(() => { if (alive) setPaceExcluded(new Set()); });
    return () => { alive = false; };
  }, [isPortal, exclusionsKey]);
  const setPaceExclusion = async (f, crop, exclude) => {
    if (isPortal || !f.farmer_id) return;
    const key = paceExclusionKey(f.farmer_id, f.year, crop);
    const cropKey = key.split("|")[2];
    setSavingExclusion(key);
    try {
      if (exclude) await sbUpsert("field_progress_exclusions", [{ farmer_id: f.farmer_id, crop_year: Number(f.year), crop: cropKey, excluded_by: getCurrentUserId() || null, excluded_at: new Date().toISOString(), reason: "no harvest data" }], "farmer_id,crop_year,crop");
      else await sbDelete("field_progress_exclusions", `farmer_id=eq.${encodeURIComponent(f.farmer_id)}&crop_year=eq.${Number(f.year)}&crop=eq.${encodeURIComponent(cropKey)}`);
      setExclusionsKey(k => k + 1);
    } catch (e) { alert("Couldn't save: " + ((e && e.message) || e)); }
    finally { setSavingExclusion(null); }
  };
  const planting = mode === "planting";
  // ⚠ NAMED POSITIVELY, BECAUSE A THIRD PANEL BROKE THE NEGATIVE FORM. These
  // guards used to read `mode !== "maturity"`, which is "anything that is not the
  // corn panel" — so the moment a second panel mode (wheat) existed, the planting
  // tables rendered UNDERNEATH it. The question they are actually asking is
  // "is this one of the two modes that draws the field table", so it says that.
  const isFieldTable = mode === "planting" || mode === "harvest";

  // In the portal, ASK for the caller's own farms by id rather than relying on the
  // server to scope an unqualified request. Both are true for a real farmer, but
  // "Preview as" runs this component with an ADMIN token — unqualified, that call
  // legitimately returns the whole book onto a portal screen.
  const scopeQ = useMemo(() => {
    const ids = (restrictFarmerIds || []).filter(Boolean);
    return ids.length ? `&farmer=${ids.map(encodeURIComponent).join(",")}` : "";
  }, [restrictFarmerIds]);

  // ── SHOW THE LAST ANSWER WHILE THE NEW ONE COMPUTES ──────────────────────────
  // Book-wide this request recomputes every connected farm and takes 11-15s
  // (measured 2026-10-16; one farm is under 2.5s). Coming back to the page meant
  // waiting through that every time. The previous result for the SAME viewer,
  // year and scope is shown at once, labelled with when it was computed, and is
  // replaced the moment the fresh one lands.
  // In MEMORY, never browser storage: entering or leaving Preview-as hard-reloads
  // the page, which empties this, so one identity's book can never be drawn under
  // another's login. Keyed by the effective user as well, and nothing is cached
  // when there is no user to key it to.
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    if (isPortal && !scopeQ) { setData({ ok: true, farmers: [] }); return; }
    const uid = getCurrentUserId();
    const cacheKey = uid ? `${uid}|${year}|${scopeQ}` : null;
    const cached = cacheKey ? _claimWatchCache.get(cacheKey) : null;
    let alive = true; setData(cached || null); setRefreshing(!!cached); setErr(null);
    authedFetch(`/api/market?fn=jd-claimwatch${year ? `&year=${year}` : ""}${scopeQ}`)
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        setRefreshing(false);
        if (d && d.error) setErr(d.error);
        else { setData(d); if (cacheKey) _claimWatchCache.set(cacheKey, d); }
      })
      .catch(e => { if (alive) { setRefreshing(false); setErr(String((e && e.message) || e)); } });
    return () => { alive = false; };
  }, [reloadKey, year, scopeQ, isPortal]);

  // Claim verdicts are agency working material and the SERVER decides who gets
  // them (admin + limited agent, never a farmer) — this only follows what
  // arrived. Keyed off the payload rather than off isPortal so a portal agent
  // keeps the view they have on the agent side.
  const showClaims = !isPortal || !!(data && data.claims);

  // JD-connected set (for the "JD Ops" badge) + planting-survey data (a pace source
  // for farms with no machine data, plus the manual-add roster).
  useEffect(() => {
    let alive = true;
    // The connected set is read on EVERY surface including the portal — it is what
    // the "JD OPS" badge asserts, and jd-connected is caller-scoped, so a farmer
    // gets only their own farms. It used to be skipped in portal, which left the
    // badge with nothing to stand on there.
    authedFetch(`/api/market?fn=jd-connected`).then(r => r.json())
      .then(d => { if (alive && d && Array.isArray(d.connections)) setConnectedIds(new Set(d.connections.map(c => c.farmer_id).filter(Boolean))); })
      .catch(() => {});
    // The planting-survey roster is the AGENCY's list of who is being chased for
    // acres, and `token` on it is the credential behind the unauthenticated
    // /planting/<token> page. None of it belongs on a portal screen, so the
    // portal doesn't read it at all — it runs on machine data alone. (RLS would
    // return nothing to a farmer anyway; it would return everything in preview.)
    if (isPortal) return () => { alive = false; };
    Promise.all([
      sbFetch("planting_survey_farmers", { select: "id,farmer_id,display_name,token,is_active", filters: ["is_active=eq.true"] }).catch(() => []),
      sbFetch("planting_progress", { select: "survey_farmer_id,crop,acres_planted,submitted_at", order: "submitted_at.asc,id.asc" }).catch(() => []),
      sbFetch("planting_intentions", { select: "survey_farmer_id,crop,intended_acres" }).catch(() => []),
    ]).then(([sf, prog, intent]) => {
      if (!alive) return;
      const progByFarmCrop = {}; // asc order → last write wins = latest submission
      (prog || []).forEach(p => { progByFarmCrop[`${p.survey_farmer_id}|${(p.crop || "").toUpperCase()}`] = p; });
      const intentByFarmCrop = {};
      (intent || []).forEach(x => { if (x.intended_acres != null) intentByFarmCrop[`${x.survey_farmer_id}|${(x.crop || "").toUpperCase()}`] = Number(x.intended_acres); });
      setSurvey({ farms: sf || [], progByFarmCrop, intentByFarmCrop });
    }).catch(() => {});
    return () => { alive = false; };
  }, [reloadKey, isPortal]);

  // Mode-aware accessors — the same UI drives Planting (seed_rate) or Harvest (yield).
  const doneAc = (a) => (planting ? a.planted_acres : a.harvested_acres) || 0;
  // The % denominator. Harvest: PLANTED insured acres, the same acres the server paces over
  // (src/fieldProgress/harvestComplete.js). Insured acres are shown as context, never divided by.
  const denAc = (a) => planting ? (a.insured_acres || 0) : fpHarvestDen(a);
  const areaComplete = (a) => planting ? a.plant_complete : a.complete;
  const areaFieldsDone = (a) => planting ? a.fields_planted : a.fields_harvested;
  const areaCompleteness = (a) => (planting ? a.plant_completeness : a.completeness) || 0;
  // ⚠ FIELD-COMPLETE IS NOT ACRE-COMPLETE, AND THE BADGE USED TO CLAIM IT WAS.
  // A field counts as harvested at 40% machine coverage, so every field can clear the
  // bar while the county is genuinely short of acres — and a ONE-FIELD county has no
  // resolution at all. Measured on the live CY2026 book 2026-10-17, five cells read
  // "ALL IN" on acres they did not have: DAVID DERMODY wheat 61.3% (1/1 fields),
  // MEISENZAHL triticale 78.4% (1/1), SULLIVAN FARMS wheat 84.4% (25/25) — 57.4 acres —
  // MEISENZAHL wheat 87.7% (1/1) and SILSBY wheat 90.0% (11/11).
  //
  // The backend sets acres_short against a measured floor and this only READS it, so
  // the two cannot disagree about where the line sits. Planting has no equivalent —
  // its pace is already acre-based — so it is never short by this test.
  const areaAcresShort = (a) => !planting && a.acres_short === true;
  const farmComplete = (f) => planting ? f.farm_planting_complete : f.farm_harvest_complete;
  // The card's ✓/green AND the header's complete counts read this ONE test, so they cannot
  // disagree. Harvest: the shown areas' own completion once a filter or the tracked split
  // has narrowed the card (see cardHarvestComplete); planting's flag is already per crop.
  const cardDone = (f) => planting ? !!farmComplete(f) : cardHarvestComplete(f, f._areasTotal);

  const allCrops = useMemo(() => {
    const s = new Set();
    if (planting) {
      (data?.farmers || []).forEach(f => (f.planting?.crops || []).forEach(c => s.add(c.crop)));
      Object.keys(survey?.intentByFarmCrop || {}).forEach(k => s.add(k.split("|")[1]));
      Object.keys(survey?.progByFarmCrop || {}).forEach(k => s.add(k.split("|")[1]));
    } else (data?.farmers || []).forEach(f => (f.areas || []).forEach(a => a.crop && s.add(a.crop)));
    return [...s].filter(Boolean).sort();
  }, [data, survey, mode]); // eslint-disable-line
  // Harvest = JD machine only (per crop×county, claim-oriented). Planting = a UNIFIED
  // list: JD machine farms ∪ planting-survey farms, keyed by farmer_id (survey id when
  // unlinked). Per crop the pace source is JD machine → survey submission; the
  // Every state the payload mentions, and every county within the CURRENT state scope.
  // ⚠ DERIVED FROM `data`, NEVER FROM `farms` — farms is already narrowed by these
  // filters, so a list built from it would shrink as you use it: choose one county and
  // the rest disappear, which reads as a book with one county in it.
  const allStates = useMemo(() => {
    const s2 = new Set();
    (data?.farmers || []).forEach(f => (f.areas || []).forEach(a => a.state && s2.add(a.state)));
    return [...s2].sort();
  }, [data]);
  const allCounties = useMemo(() => {
    const m = new Map();
    (data?.farmers || []).forEach(f => (f.areas || []).forEach(a => {
      const st = a.state || "";
      if (stateFilter && st !== stateFilter) return;
      const nm = String(a.county || "").toUpperCase();
      if (!nm) return;
      // Keyed STATE|NAME so ERIE NY and ERIE PA are two entries, not one.
      m.set(`${st}|${nm}`, { key: `${st}|${nm}`, state: st, name: nm });
    }));
    return [...m.values()].sort((x, y) => (x.state || "").localeCompare(y.state || "") || x.name.localeCompare(y.name));
  }, [data, stateFilter]);

  // denominator is the backend waterfall (JD) or the survey's intended acres.
  const farmsAll = useMemo(() => {
    if (!planting) {
      // ⚠ THE CORN TYPE NARROWS, IT NEVER RE-KEYS. The backend folds CORN SILAGE into CORN
      // on purpose — un-keying it strands grain-insured corn that was chopped, which is the
      // change of intent this screen most needs to show — so the type arrives as a pair of
      // acre figures ON the corn row and this filter selects rows that have activity of the
      // chosen type. An area with neither figure (type never stated) is kept under "All"
      // and shown by neither branch, rather than being guessed into one.
      const typeOk = (a) => {
        if (!cornType || String(a.crop || "").toUpperCase() !== "CORN") return true;
        const ins = cornType === "silage" ? a.insured_silage_acres : a.insured_grain_acres;
        const harv = cornType === "silage" ? a.harvested_silage_acres : a.harvested_grain_acres;
        return (ins || 0) > 0.05 || (harv || 0) > 0.05;
      };
      // ⚠ KEYED STATE|NAME, NEVER THE BARE NAME. Matching on the name alone would draw
      // Pennsylvania's ERIE acres under a New York selection and vice versa.
      const stOk = (a) => !stateFilter || String(a.state || "") === stateFilter;
      const ctOk = (a) => !countySel.size || countySel.has(`${a.state || ""}|${String(a.county || "").toUpperCase()}`);
      return (data?.farmers || []).map(f => ({ ...f, _areasTotal: (f.areas || []).length, areas: (f.areas || []).filter(a => (!cropFilter || a.crop === cropFilter) && typeOk(a) && stOk(a) && ctOk(a)) })).filter(f => f.areas.length > 0);
    }
    const byKey = {};
    (data?.farmers || []).forEach(f => {
      const key = f.farmer_id;
      const u = (byKey[key] ||= { key, farmer_id: f.farmer_id, survey_farmer_id: null, farmer: f.farmer, year: f.year, jdOps: connectedIds.has(f.farmer_id), survey: false, token: null, _rows: {} });
      (f.planting?.crops || []).forEach(c => {
        u._rows[c.crop] = { crop: c.crop, county: null, insured_acres: c.denom || 0, planted_acres: c.planted_acres, plant_complete: c.complete, plant_completeness: c.coverage || 0, fields_planted: null, denom: c.denom, denom_source: c.denom_source, intended_acres: c.intended_acres, prior_insured: c.prior_insured, current_insured: c.current_insured, by_county: c.by_county || [], src: "jd" };
      });
    });
    (survey?.farms || []).forEach(s => {
      const key = s.farmer_id || `survey:${s.id}`;
      const u = (byKey[key] ||= { key, farmer_id: s.farmer_id || null, survey_farmer_id: s.id, farmer: s.display_name, year: Number(year) || CURRENT_CROP_YEAR, jdOps: s.farmer_id ? connectedIds.has(s.farmer_id) : false, survey: false, token: null, _rows: {} });
      u.survey = true; u.token = s.token; u.survey_farmer_id = s.id;
      const crops = new Set();
      Object.keys(survey.progByFarmCrop).forEach(k => { const [sid, crop] = k.split("|"); if (sid === s.id) crops.add(crop); });
      Object.keys(survey.intentByFarmCrop).forEach(k => { const [sid, crop] = k.split("|"); if (sid === s.id) crops.add(crop); });
      crops.forEach(crop => {
        if (u._rows[crop] && u._rows[crop].src === "jd") return; // JD machine wins per crop
        const prog = survey.progByFarmCrop[`${s.id}|${crop}`];
        const planted = prog ? Number(prog.acres_planted) || 0 : 0;
        const denom = survey.intentByFarmCrop[`${s.id}|${crop}`] ?? null;
        const cov = denom ? Math.min(1, planted / denom) : null;
        u._rows[crop] = { crop, county: null, insured_acres: denom || 0, planted_acres: planted, plant_complete: cov != null && cov >= PLANT_DONE, plant_completeness: cov || 0, fields_planted: null, denom, denom_source: denom != null ? "intended" : "none", intended_acres: denom, prior_insured: 0, current_insured: 0, by_county: [], src: "survey", last_submit: prog?.submitted_at || null };
      });
    });
    return Object.values(byKey).map(u => {
      const areas = Object.values(u._rows).filter(r => !cropFilter || r.crop === cropFilter);
      return { ...u, areas, farm_planting_complete: areas.length > 0 && areas.every(r => r.plant_complete) };
    }).filter(u => u.areas.length > 0);
    // ⚠ cornType IS A DEPENDENCY. This memo does the filtering, so leaving it out would
    // make the control inert — the button would highlight and the list would not move,
    // which reads as "this farm has no silage" rather than as a dead control.
  }, [data, survey, connectedIds, cropFilter, cornType, stateFilter, countySel, mode, year]); // eslint-disable-line
  // ⚠ A FARM WITH NO MACHINE SOURCE FOR A CROP IS NOT "AWAITING" ITS DATA — IT WILL NEVER
  // COME. The backend stamps every area tracked / not tracked (src/fieldProgress/
  // trackedSource.js); this splits on that stamp ONCE, so the pace, the operations count,
  // To review and the card list all read the same partition. Not-tracked areas are listed
  // in their own group below the cards, never dropped. Harvest only — planting has its
  // own survey source.
  const fpSplit = useMemo(() => planting ? { tracked: farmsAll, untracked: [] } : partitionTracked(farmsAll), [farmsAll, planting]);
  // Then the agent's exclusions (harvest only, agency only): the excluded crop leaves the
  // pace, the cards and To review, and is listed below with a one-click re-include.
  const fpPace = useMemo(() => (planting || isPortal) ? { paced: fpSplit.tracked, excluded: [] } : partitionPaceExcluded(fpSplit.tracked, paceExcluded),
    [fpSplit, planting, isPortal, paceExcluded]);
  const farms = fpPace.paced;
  const excludedFarms = fpPace.excluded;
  const excludedSummary = useMemo(() => paceExclusionSummary(excludedFarms), [excludedFarms]);
  // The farm's UNFILTERED areas, for the "no harvest data arriving?" hint — a crop filter
  // must not hide the other crop whose passes prove the data is arriving.
  const allAreasByFarm = useMemo(() => {
    const m = new Map();
    (data?.farmers || []).forEach(f => m.set(`${f.farmer_id}|${f.year}`, f.areas || []));
    return m;
  }, [data]);
  const untrackedFarms = fpSplit.untracked;
  const [showUntracked, setShowUntracked] = useState(false);
  const shownYears = useMemo(() => [...new Set(farmsAll.map(f => f.year).filter(Boolean))].sort((a, b) => b - a), [farmsAll]);

  // ⚠ A FARM WITH NO MACHINE DATA IS NOT AT 0% — IT IS UNMEASURED, AND IT LEAVES
  // THE DENOMINATOR. The pace is machine acres over insured acres; a farm whose
  // machine half is absent contributes 0 to the numerator and its whole acreage to
  // the denominator, so every such farm drags the book pace down by its own size
  // and reads on its own card as a crop nobody has harvested. DIAMOND D FARMING
  // (no connection, no operations, 3,017 insured ac) did exactly that on a
  // customer's portal. They are counted as `noData` and named, never folded into
  // "in progress" — "we have no machine data for this farm" and "this farm has
  // harvested nothing" are different statements and only one of them is ours to
  // make.
  const totals = useMemo(() => {
    let insured = 0, done = 0, claims = 0, estInd = 0, complete = 0, inProgress = 0, review = 0, noData = 0;
    farms.forEach(f => {
      if (f.has_machine_data === false) { noData++; return; }
      const areas = f.areas || [];
      if (cardDone(f)) complete++; else inProgress++;
      areas.forEach(a => {
        insured += denAc(a); done += doneAc(a);
        if (!planting && a.likely_claim) { claims++; estInd += a.est_indemnity || 0; }
        if (!planting && a.missing_data_acres >= 5) review++;
      });
    });
    // A claim flag never depends on whether the crop is tracked: a not-tracked area that
    // somehow carries one is still counted here, though it leaves the pace.
    let untrackedInsured = 0;
    untrackedFarms.forEach(f => (f.areas || []).forEach(a => {
      untrackedInsured += a.insured_acres || 0;
      if (!planting && a.likely_claim) { claims++; estInd += a.est_indemnity || 0; }
    }));
    // Same for an agent-excluded crop: out of the pace, never out of the claim count.
    excludedFarms.forEach(f => (f.areas || []).forEach(a => {
      if (a.likely_claim && !planting) { claims++; estInd += a.est_indemnity || 0; }
    }));
    return { insured, done, claims, estInd, complete, inProgress, review, noData,
             untracked: untrackedFarms.length, untrackedInsured,
             ops: farms.length - noData, pace: insured > 0 ? done / insured : 0 };
  }, [farms, untrackedFarms, excludedFarms, mode]); // eslint-disable-line

  const ranked = useMemo(() => {
    const paceOf = (f) => { let ins = 0, d = 0; (f.areas || []).forEach(a => { ins += denAc(a); d += doneAc(a); }); return ins > 0 ? d / ins : 0; };
    const acOf = (f) => (f.areas || []).reduce((s, a) => s + (a.insured_acres || 0), 0);
    const claimsOf = (f) => (f.areas || []).filter(a => a.likely_claim).length;
    const arr = [...farms];
    if (sort === "claims" && !planting) arr.sort((a, b) => (claimsOf(b) - claimsOf(a)) || (paceOf(b) - paceOf(a)));
    else if (sort === "acres") arr.sort((a, b) => acOf(b) - acOf(a));
    else if (sort === "name") arr.sort((a, b) => String(a.farmer).localeCompare(String(b.farmer)));
    else arr.sort((a, b) => paceOf(b) - paceOf(a));
    return arr;
  }, [farms, sort, mode]); // eslint-disable-line

  const toggle = (id) => setExpanded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 12, padding: isMobile ? 14 : 18 };
  const pct = (c) => Math.round((c || 0) * 100);
  const barColor = (done) => done ? "#4a9a4a" : (planting ? "#6aa0d0" : "#d8a93a");
  const verb = planting ? "Planted" : "Harvested";
  const SRC_LABEL = { intended: "intended", insured: "insured", "prior-year": "last yr", none: "no baseline" };
  // Persist the agent-entered intended acres → planting_intended_acres (the override
  // that wins over the prior-year default). Reloads so the denominator recomputes.
  const saveIntended = async (f, crop, acres) => {
    const key = `${f.key || f.farmer_id}|${crop}`; setSavingIntended(key);
    const val = acres === "" || acres == null ? null : Number(acres);
    try {
      if (f.farmer_id) {
        await sbUpsert("planting_intended_acres", [{ farmer_id: f.farmer_id, crop_year: Number(f.year) || CURRENT_CROP_YEAR, crop, intended_acres: val, updated_at: new Date().toISOString() }], "farmer_id,crop_year,crop");
      } else if (f.survey_farmer_id) {
        await sbUpsert("planting_intentions", [{ survey_farmer_id: f.survey_farmer_id, crop, intended_acres: val, updated_at: new Date().toISOString() }], "survey_farmer_id,crop");
      }
      setReloadKey(k => k + 1);
    } catch (e) { alert("Couldn't save intended acres: " + ((e && e.message) || e)); }
    finally { setSavingIntended(null); }
  };
  // Add an insured grower to the tracker → creates a planting-survey row (linked to
  // the book by farmer_id) with a tokenised survey link. They then appear here and
  // can self-report planting via /planting/<token>, or the agent enters intended.
  const addFarm = async (bookFarmer) => {
    try {
      const token = ((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2))).replace(/-/g, "");
      await sbInsert("planting_survey_farmers", [{ farmer_id: bookFarmer.id, display_name: bookFarmer.name, token, is_active: true }]);
      setAddOpen(false); setAddQuery(""); setReloadKey(k => k + 1);
    } catch (e) { alert("Couldn't add farm: " + ((e && e.message) || e)); }
  };
  // Book growers not already tracked (no JD claimwatch row, no survey row) — the
  // search-add candidates. Deduped by normalized name.
  const trackedNames = useMemo(() => {
    const s = new Set();
    (data?.farmers || []).forEach(f => s.add((f.farmer || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "")));
    (survey?.farms || []).forEach(f => s.add((f.display_name || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "")));
    return s;
  }, [data, survey]);
  const addCandidates = useMemo(() => {
    const q = addQuery.trim().toLowerCase(); if (!q) return [];
    const seen = new Set();
    return (farmers || []).filter(f => {
      const nm = (f.name || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
      if (!nm || seen.has(nm) || trackedNames.has(nm)) return false;
      if (!(f.name || "").toLowerCase().includes(q)) return false;
      seen.add(nm); return true;
    }).slice(0, 8);
  }, [farmers, addQuery, trackedNames]);
  // Custom-harvest attribution is now automatic: owner-scoped CLU attribution
  // (jd_field_ftf) credits acres a combine ran on ANOTHER insured's land to that
  // insured directly (Silsby → Ranney), so the old manual "attribute to insured"
  // flag/flow on unmatched acres is obsolete and removed.

  // "Book" is the agency's word for the whole customer list — on a farmer's own
  // page the same number is just their pace, so the label follows the reader.
  const paceLabel = isPortal ? "Pace" : "Book Pace";
  // WHAT THE PACE IS A FRACTION OF. The denominator is not always insured acres:
  // per crop it is the agent's intended-acres override, else current-year insured,
  // else last year's insured (see denom_source / SRC_LABEL). Calling all three
  // "insured ac" made a pace against last year's acres read as a pace against a
  // filed acreage report. Name whichever it actually is, and say "mixed" rather
  // than pick one when a multi-crop total spans several.
  const baseSrc = useMemo(() => {
    const s = new Set();
    farms.forEach(f => (f.areas || []).forEach(a => { if (a.denom_source && a.denom_source !== "none") s.add(a.denom_source); }));
    return s.size === 1 ? (SRC_LABEL[[...s][0]] || [...s][0]) : s.size ? "mixed" : null;
  }, [farms]); // eslint-disable-line
  // ── THE WEIGHTED AVERAGE YIELD OF EVERYTHING HARVESTED SO FAR ───────────────
  //
  // Kevin, 2026-10-07: *"add a weighted average yield for all harvested acres by crop when
  // I have a crop selected. Then also let that average weighted yield change when we have
  // the filter by state, county, multiple counties etc. I want to be able to see: What is
  // livingston county soybean yield so far?"*
  //
  // It is computed over `farms`, which is the memo the crop, corn-type, state and county
  // filters have ALREADY narrowed — so it answers the filtered question by construction
  // rather than by a second set of filters that could drift from the first.
  //
  // ⚠ THE DENOMINATOR IS `production_acres`, NOT `harvested_acres`, AND THEY ARE NOT THE
  // SAME NUMBER. A yield is the bushels over the acres whose monitor pass validYield
  // ACCEPTED; a bad pass leaves the numerator and the denominator together. Dividing real
  // bushels by every harvested acre would read LOW — Hart's corn is 23,606 bu over 95.8
  // measured acres, and his harvested figure is the same here only because every pass was
  // sane. The backend exposes both for exactly this reason.
  //
  // ⚠ AND IT IS PER UNIT, NEVER BLENDED. Corn grain is bu/ac and chopped corn is ton/ac;
  // dry beans are lb/ac because that is what RMA's APH for them is written in. Averaging
  // across units is the conflation that printed Lismore's Steuben corn at 86.5 bu from
  // (154 bu + 18.9 ton)/2. Grain and silage are two figures on two axes, and a filter that
  // somehow spans two grain units reports neither rather than one of them.
  //
  // ⚠ ONLY UNDER A CROP SELECTION. "The average yield across corn, soybeans and wheat" is
  // not a quantity — they are different crops in the same unit, which is worse than a unit
  // mismatch because the number looks reasonable.
  const yieldAvg = useMemo(() => {
    if (planting || !cropFilter) return null;
    let prod = 0, pac = 0, silTons = 0, silAc = 0, farmsIn = 0, noUnit = 0;
    const units = new Set();
    farms.forEach(f => {
      let counted = false;
      (f.areas || []).forEach(a => {
        const pa = Number(a.production_acres) || 0;
        const pb = Number(a.production_bu);
        if (pa > 0.05 && Number.isFinite(pb) && pb > 0) {
          // ⚠ AN ABSENT UNIT IS EXCLUDED AND COUNTED, NEVER ASSUMED TO BE BUSHELS. The
          // server states the unit it stored the yield in (dry beans are POUNDS, and a
          // 60x error once printed 1,881 lb/ac as 1,881 bu/ac on a panel headed LIKELY
          // CLAIM). Where it says nothing, the client may not decide for it — that is the
          // rule check-jd-forage-units holds, and defaulting here broke it.
          if (!a.yield_unit) { noUnit += pa; return; }
          prod += pb; pac += pa; counted = true;
          units.add(a.yield_unit);
        }
        const sa = Number(a.silage_measured_acres) || 0;
        const st = Number(a.silage_tons);
        if (sa > 0.05 && Number.isFinite(st) && st > 0) { silTons += st; silAc += sa; counted = true; }
      });
      if (counted) farmsIn++;
    });
    const grain = pac > 0.05 && units.size === 1
      ? { yield: prod / pac, acres: pac, unit: [...units][0] } : null;
    const silage = silAc > 0.05 ? { yield: silTons / silAc, acres: silAc, unit: "wet ton/ac" } : null;
    return {
      grain, silage, farms: farmsIn,
      // Reported, never silent: a filter that spans two grain units gets no blended
      // number and a reason instead.
      mixedUnits: pac > 0.05 && units.size > 1 ? [...units].sort() : null,
      // Acres whose yield carries no stated unit. Reported so a short average says why.
      noUnitAcres: noUnit > 0.05 ? noUnit : null,
    };
  }, [farms, cropFilter, planting]);
  const baseNote = baseSrc ? ` (${baseSrc})` : "";
  const kpis = planting
    ? [
        ["Operations", String(totals.ops), `${totals.complete} fully planted · ${totals.inProgress} in progress${totals.noData ? ` · ${totals.noData} no machine data` : ""}`, "#1a2e1a", "#fff"],
        [paceLabel, `${pct(totals.pace)}%`, `${fmt(totals.done, 0)} / ${fmt(totals.insured, 0)} ac baseline${baseNote}`, "#2d4a2d", "#fff"],
        ["Acres Planted", fmt(totals.done, 0), isPortal ? "from your machine data" : "across connected operations", "#2d4a2d", "#fff"],
        // The rule is 90% of the baseline, not 95%, and it is deliberately short
        // of 100: machine coverage never reaches the reported acres (headlands,
        // waterways, GPS gaps), so a finished field lands in the low-to-mid 90s.
        // This card claimed 95%+ while the rule said 90%, which is how a farm at
        // 94% came to read "Complete" beside a card saying it needed 95.
        ["Fully Planted", `${totals.complete}/${totals.ops}`, `operations ${Math.round(PLANT_DONE * 100)}%+ of baseline`, "#2d4a2d", "#f4faf4"],
      ]
    : [
        ["Operations", String(totals.ops), `${totals.complete} complete · ${totals.inProgress} in progress${totals.noData ? ` · ${totals.noData} no machine data` : ""}`, "#1a2e1a", "#fff"],
        // The exclusion is STATED on the headline it changes, so a higher % never reads as
        // a faster harvest when it is a narrower book.
        [paceLabel, `${pct(totals.pace)}%`, `${fmt(totals.done, 0)} / ${fmt(totals.insured, 0)} planted ac${excludedSummary.farms ? ` · ${excludedSummary.farms} farm${excludedSummary.farms === 1 ? "" : "s"} excluded (${fmt(excludedSummary.acres, 0)} ac)` : ""}`, "#2d4a2d", "#fff"],
        // Claim verdicts + the internal data-QC flag are agency-side only; a
        // farmer gets acres and yield instead, which is what the machine measured.
        ...(showClaims
          ? [["Likely Claims", String(totals.claims), totals.estInd > 0 ? `~${fmtD0(totals.estInd)} · machine-yield screener` : totals.claims > 0 ? "machine-yield screener" : "none flagged", totals.claims > 0 ? "#7a1f10" : "#2d4a2d", totals.claims > 0 ? "#fdf0ec" : "#fff"],
             ["To Review", String(totals.review), "insured fields missing machine data", "#7a5a00", totals.review > 0 ? "#fff8ec" : "#fff"]]
          // Harvest completion is counted in FIELDS, not acres — a field counts
          // once its machine coverage clears a headland-tolerant floor — so this
          // subtitle has to say fields or it describes the wrong denominator.
          : [["Acres Harvested", fmt(totals.done, 0), "from your machine data", "#2d4a2d", "#fff"],
             ["Fully Harvested", `${totals.complete}/${totals.ops}`, `operations with ${Math.round(HARVEST_DONE * 100)}%+ of fields harvested`, "#2d4a2d", "#f4faf4"]]),
      ];
  // ⚠ APPENDED, NOT SUBSTITUTED. It only exists under a crop selection, so the four cards
  // above are what every other view still shows. The subtitle names the ACRES the average
  // was taken over — a yield with no stated denominator is not checkable, and these acres
  // are the measured ones rather than every harvested acre.
  if (yieldAvg && yieldAvg.grain)
    kpis.push([`${cropFilter === "CORN" && yieldAvg.silage ? "Grain yield" : "Avg yield"}`,
      `${yieldAvg.grain.yield.toFixed(1)}`,
      `${yieldAvg.grain.unit} over ${fmt(yieldAvg.grain.acres, 0)} measured ac · ${yieldAvg.farms} op${yieldAvg.farms === 1 ? "" : "s"}`,
      "#1a3a52", "#fff"]);
  if (yieldAvg && yieldAvg.silage)
    kpis.push(["Silage yield", `${yieldAvg.silage.yield.toFixed(1)}`,
      `wet ton/ac over ${fmt(yieldAvg.silage.acres, 0)} measured ac`, "#1a3a52", "#fff"]);
  // A filter spanning two grain units gets a NAMED refusal, never a blended number.
  if (yieldAvg && yieldAvg.mixedUnits)
    kpis.push(["Avg yield", "—",
      `not comparable — this selection spans ${yieldAvg.mixedUnits.join(" and ")}`, "#7a5a00", "#fff8ec"]);
  const sortOpts = planting ? [["pace", "Planting pace"], ["acres", "Insured acres"], ["name", "Name"]]
                            : [...(showClaims ? [["claims", "Likely claims"]] : []), ["pace", "Harvest pace"], ["acres", "Insured acres"], ["name", "Name"]];

  const modeBtn = (m, icon, label) => (
    <button onClick={() => { setMode(m); setExpanded(new Set()); if (sort === "claims" && m === "planting") setSort("pace"); }}
      style={{ cursor: "pointer", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 800, background: mode === m ? "#2d4a2d" : "#e3ece3", color: mode === m ? "#fff" : "#2d4a2d" }}>{icon} {label}</button>
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, color: T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>Field Progress</h2>
            {/* ⚠ THE CHIP BELONGS TO THE FIELD TABLE, AND IT CONTRADICTED THE PANEL
                BESIDE IT. On the Wheat tab it read "Crop year 2026" over a CY2027
                wheat report, because `year` is the field table's own crop year and
                the wheat panel carries its own. A header that disagrees with its
                own numbers is the MI-brief "WNY Corn" defect in a new place — the
                panel states its crop year in its own headline, so the chip simply
                does not render for modes the field table does not drive. */}
            {data && isFieldTable && (
              <span style={{ fontSize: 12, fontWeight: 800, color: "#2d4a2d", background: "#e8f0e8", border: "1px solid #b8d0b8", borderRadius: 20, padding: "3px 12px", letterSpacing: "0.04em" }}>
                {year ? `Crop year ${year}` : shownYears.length === 1 ? `Crop year ${shownYears[0]}` : shownYears.length > 1 ? `Crop years ${shownYears[shownYears.length - 1]}–${shownYears[0]}` : "Latest year"}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#60735f", marginTop: 3 }}>
            {/* ⚠ A FALL-THROUGH DESCRIPTION DESCRIBES THE WRONG PAGE. This chain
                ended at the HARVEST sentence, so the Wheat tab was subtitled "Live
                harvest pace & average yield — with likely-claim flags" over a
                planting report. Every mode names itself now; there is no default. */}
            {mode === "maturity" ? "Predicted corn black-layer date per field — plant date, variety & this year's heat — across every connected farm."
                      : mode === "wheat" ? (isPortal ? "Your winter wheat planting, straight from your John Deere machine data."
                                                     : "Winter wheat going in the ground now — measured acres against what each farm usually plants.")
                      : planting ? (isPortal ? "Your planting pace, straight from your John Deere machine data."
                                             : "Live planting pace by operation across every John Deere–connected farm.")
                      : isPortal ? "Your harvest pace and average yield, straight from your John Deere machine data."
                      : "Live harvest pace & average yield — with likely-claim flags — by operation."}
            {isFieldTable && cropFilter ? ` · ${cropFilter}` : ""}
          </div>
          {/* Same reasoning as the crop-year chip: this stamp is the FIELD TABLE's
              own generated_at, so it must not sit over a panel fed by a different
              endpoint and claim to date it. */}
          {/* ⚠ THE DATE A GROWER NEEDS IS THE MACHINE'S, NOT THIS REQUEST'S.
              This printed `generated_at`, which is when the request ran — so the
              page read "as of <today>" however old the Deere data was, and a farm
              whose uploads stopped weeks ago looked current in the middle of
              harvest. `data_freshness` carries two dates because they fail
              separately: a QUIET FARM has old machine data and a recent sync; a
              BROKEN PIPE has both old, and only the second is ours to fix.
              No machine data at all stays UNKNOWN — it never borrows today. */}
          {isFieldTable && data?.data_freshness && (data.data_freshness.machineAsOf || data.data_freshness.syncedAt) ? (
            <FreshAsOf
              date={String(data.data_freshness.machineAsOf || data.data_freshness.syncedAt).slice(0, 10)}
              prefix={data.data_freshness.machineAsOf ? "newest machine data" : "last checked"}
              style={{ marginTop: 5, display: "inline-block" }} />
          ) : isFieldTable && data ? (
            <span style={{ marginTop: 5, display: "inline-block", fontSize: 12, color: "#8a6d1f", fontWeight: 700 }}>
              No machine data on file for these farms
            </span>
          ) : null}
          {isFieldTable && refreshing && data?.generated_at && (
            <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 700, color: "#8a6d1f" }}>
              · showing results from {new Date(data.generated_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} — refreshing…
            </span>
          )}
          {isFieldTable && data?.data_freshness?.syncedAt && (
            <span style={{ marginLeft: 8, fontSize: 11.5, color: "#60735f" }}>
              {/* Same format as the machine date beside it ("Oct 10, 2026"), and parsed
                  at local noon the way FreshAsOf does, so a bare date never slips a
                  day west of UTC. It printed raw ISO ("2026-10-15") next to it. */}
              · last checked {new Date(String(data.data_freshness.syncedAt).slice(0, 10) + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
          )}
        </div>
        <button onClick={() => setReloadKey(k => k + 1)} style={{ background: "#2d4a2d", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>↻ Refresh</button>
      </div>

      {/* Planting / Harvest toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {modeBtn("planting", "🌱", "Planting")}
        {modeBtn("harvest", "🌾", "Harvest")}
        {modeBtn("maturity", "🌽", "Corn Maturity")}
        {modeBtn("wheat", "🌾", "Wheat Planting")}
      </div>

      {mode === "maturity" && <CornMaturityPanel farmers={farmers} isMobile={isMobile} isPortal={isPortal} restrictFarmerIds={restrictFarmerIds} />}
      {mode === "wheat" && <WheatPlantingPanel isMobile={isMobile} isPortal={isPortal} restrictFarmerIds={restrictFarmerIds} />}

      {isFieldTable && err && <div style={{ ...card, color: "#c0392b", fontSize: 13 }}>Couldn't load field data: {err}</div>}
      {isFieldTable && !data && !err && <div style={{ ...card, color: "#60735f", fontSize: 13 }}><UiSpinner />{scopeQ ? "Reading your field data…" : "Reading field data across the book — every connected farm is recomputed, which takes about 15 seconds. Coming back to this page shows the last result straight away."}</div>}

      {isFieldTable && data && !err && (
        <>
          {/* ⚠ NOTHING MEASURED MEANS NO FIGURES, NOT FIGURES OF ZERO. Every KPI
              here is computed over the farms that HAVE machine data; when none
              do, the strip reads "PACE 0% · 0 / 0 insured ac · FULLY HARVESTED
              0/0" — the missing-data-as-zero class this page was just fixed for,
              one level up from the per-farm card. Rendering the count of what we
              could not measure is the honest replacement, and the connect panel
              and the farm cards below already say the rest. */}
          {totals.ops === 0 ? (
            <div style={{ ...card, marginBottom: 16, color: "#60735f", fontSize: 13, lineHeight: 1.55 }}>
              {totals.noData > 0
                ? <>No machine data for {totals.noData === 1 ? "this operation" : `any of these ${totals.noData} operations`} yet</>
                : <>No machine data source{totals.untracked === 1 ? " for this farm" : totals.untracked > 1 ? ` for these ${totals.untracked} farms` : ""}</>}
              {cropFilter ? ` for ${cropFilter.toLowerCase()}` : ""}
              {year ? ` in ${year}` : ""}, so there is no {planting ? "planting" : "harvest"} pace to report.
            </div>
          ) : (
          // Phone: two cards per row. One per row, four cards filled the whole
          // first screen before a single field appeared.
          <div style={isMobile ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }
                               : { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            {kpis.map(([label, val, sub, col, bg]) => (
              <div key={label} style={{ ...card, ...(isMobile ? { minWidth: 0, padding: "12px 14px" } : { flex: "1 1 200px", minWidth: 160 }), background: label === "Book Pace" ? "#12240f" : bg }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: label === "Book Pace" ? "#8fbf6a" : "#677167" }}>{label}</div>
                <div style={{ fontSize: isMobile ? 22 : 30, fontWeight: 800, marginTop: 4, color: label === "Book Pace" ? "#fff" : col }}>{val}</div>
                <div style={{ fontSize: 11.5, color: label === "Book Pace" ? "#9ab88a" : "#60735f", marginTop: 2 }}>{sub}</div>
              </div>
            ))}
          </div>
          )}

          {/* Rolled-up historical survey pace (planting only) */}
          {/* Book-wide survey pace across every tracked grower — an agency view,
              and it reads the survey tables directly. Not a portal surface. */}
          {planting && !isPortal && <PlantingPaceChart isMobile={isMobile} />}

          {/* Controls: crop + crop-year + sort */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <select value={cropFilter} onChange={e => { setCropFilter(e.target.value); setCornType(""); }} title="Show pace for one crop — each farm gets its own bar for that crop"
              style={{ fontSize: 12, fontWeight: 700, padding: "6px 9px", borderRadius: 7, border: "1px solid #cdd9cd", background: "#fff", color: "#2d4a2d", cursor: "pointer" }}>
              <option value="">All crops</option>
              {allCrops.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {/* ⚠ CORN ONLY, AND ONLY WHEN CORN IS SELECTED. Every other crop in this book is
                single-type, so offering grain/silage on wheat would be a control that can
                only ever narrow to nothing. Measured 2026-10-07: 25,961 CY2026 silage acres
                across 71 coverages, and 61 of 373 (farm, county) corn cells carry BOTH types
                — 42 farms whose corn row is currently one bar for two different crops. */}
            {String(cropFilter || "").toUpperCase() === "CORN" && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                {[["", "Both"], ["grain", "Grain"], ["silage", "Silage"]].map(([v, lab]) => (
                  <button key={v || "all"} onClick={() => setCornType(v)}
                    title={v === "silage"
                      ? "Corn insured as type 026, or acres John Deere reports on a forage basis"
                      : v === "grain" ? "Corn insured as type 016, or acres combined for grain"
                      : "Both corn types"}
                    style={{ cursor: "pointer", border: "1px solid #cdd9cd", borderRadius: 7,
                             padding: "6px 10px", fontSize: 12, fontWeight: 700,
                             background: cornType === v ? "#2d4a2d" : "#f6faf6",
                             color: cornType === v ? "#fff" : "#2d4a2d" }}>{lab}</button>
                ))}
              </span>
            )}
            {allStates.length > 1 && (
              <select value={stateFilter} onChange={e => { setStateFilter(e.target.value); setCountySel(new Set()); }}
                title="Filter to one state. County names are not unique across states — ERIE and COLUMBIA are each in NY and PA — so changing the state clears the county picks."
                style={{ fontSize: 12, fontWeight: 700, padding: "6px 9px", borderRadius: 7, border: "1px solid #cdd9cd", background: "#fff", color: "#2d4a2d", cursor: "pointer" }}>
                <option value="">All states</option>
                {allStates.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            )}
            {allCounties.length > 1 && (
              <details style={{ position: "relative" }}>
                <summary style={{ cursor: "pointer", listStyle: "none", fontSize: 12, fontWeight: 700, padding: "6px 9px", borderRadius: 7, border: `1px solid ${countySel.size ? "#2d4a2d" : "#cdd9cd"}`, background: countySel.size ? "#2d4a2d" : "#fff", color: countySel.size ? "#fff" : "#2d4a2d" }}>
                  {countySel.size ? `${countySel.size} count${countySel.size === 1 ? "y" : "ies"}` : "All counties"} ▾
                </summary>
                {/* Several counties at once, which is the ask — a single-select cannot
                    answer "how is the west side doing". The state is shown per row only
                    when more than one is in scope, so ERIE NY and ERIE PA stay distinct. */}
                <div style={{ position: "absolute", zIndex: 20, marginTop: 4, maxHeight: 260, overflowY: "auto", minWidth: 190, background: "#fff", border: "1px solid #cdd9cd", borderRadius: 8, boxShadow: "0 6px 18px rgba(0,0,0,.12)", padding: 6 }}>
                  <button onClick={() => setCountySel(new Set())}
                    style={{ cursor: "pointer", width: "100%", textAlign: "left", border: "none", background: "none", fontSize: 11.5, fontWeight: 700, color: "#6a7a6a", padding: "4px 6px" }}>Clear</button>
                  {allCounties.map(c => (
                    <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 6px", fontSize: 12, color: "#2d4a2d", cursor: "pointer" }}>
                      <input type="checkbox" checked={countySel.has(c.key)}
                        onChange={() => setCountySel(prev => { const n = new Set(prev); if (n.has(c.key)) n.delete(c.key); else n.add(c.key); return n; })} />
                      <span>{c.name}{!stateFilter && allStates.length > 1 && c.state ? <span style={{ color: "#8aa08a", fontWeight: 700 }}> · {c.state}</span> : null}</span>
                    </label>
                  ))}
                </div>
              </details>
            )}
            <select value={year} onChange={e => setYear(e.target.value)} title="Crop year (Latest = each farm's most recent)"
              style={{ fontSize: 12, fontWeight: 700, padding: "6px 9px", borderRadius: 7, border: "1px solid #cdd9cd", background: "#fff", color: "#2d4a2d", cursor: "pointer" }}>
              <option value="">Latest year</option>
              {[CURRENT_CROP_YEAR, CURRENT_CROP_YEAR - 1, CURRENT_CROP_YEAR - 2].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            {farms.length > 1 && (<>
              <span style={{ fontSize: 10.5, color: "#677167", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", marginLeft: 4 }}>Sort</span>
              {sortOpts.map(([k, lab]) => (
                <button key={k} onClick={() => setSort(k)} style={{ cursor: "pointer", border: "1px solid #cdd9cd", borderRadius: 7, padding: "5px 11px", fontSize: 11.5, fontWeight: 700, background: sort === k ? "#2d4a2d" : "#f6faf6", color: sort === k ? "#fff" : "#2d4a2d" }}>{lab}</button>
              ))}
            </>)}
          </div>

          {/* Add an insured grower to the tracker (planting only) — searches the
              whole customer list, so it is agent-only. */}
          {planting && !isPortal && (
            <div style={{ marginBottom: 12 }}>
              {!addOpen ? (
                <button onClick={() => setAddOpen(true)} style={{ cursor: "pointer", border: "1px dashed #b8d0b8", borderRadius: 8, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, background: "#f4faf4", color: "#2d6a2d" }}>+ Add a farm to track</button>
              ) : (
                <div style={{ ...card, padding: 12 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                    <input autoFocus value={addQuery} onChange={e => setAddQuery(e.target.value)} placeholder="Search insured growers by name…" style={{ flex: 1, fontSize: 13, padding: "8px 10px", borderRadius: 7, border: "1px solid #cdd9cd" }} />
                    <button onClick={() => { setAddOpen(false); setAddQuery(""); }} style={{ cursor: "pointer", border: "none", background: "#eef2ee", borderRadius: 7, padding: "8px 12px", fontSize: 12, fontWeight: 700, color: "#2d4a2d" }}>Cancel</button>
                  </div>
                  {addQuery.trim() && !addCandidates.length && <div style={{ fontSize: 12, color: "#677167", padding: "4px 2px" }}>No untracked insured growers match “{addQuery}”.</div>}
                  {addCandidates.map(f => (
                    <div key={f.id} onClick={() => addFarm(f)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderTop: "1px solid #f0f4f0", cursor: "pointer" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a" }}>{f.name}{f.county ? <span style={{ fontSize: 11, color: "#677167", fontWeight: 500 }}> · {f.county} Co.</span> : null}</span>
                      <span style={{ fontSize: 11.5, color: "#2d6a2d", fontWeight: 700 }}>+ Add</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Empty on the portal side almost always means "not connected yet",
              which is a step the grower can take rather than a dead end. */}
          {/* ⚠ NOT ONLY AN EMPTY LIST. A farm with insured acres and no machine data
              produces a row, so `farms.length` is 1 and this gate was skipped — the
              page then rendered 0% against 3,017 acres of a customer's corn. The
              question the panel answers is "have we any machine data for you", and
              that is the condition it is gated on. */}
          {!!farms.length && isPortal && farms.every(f => f.has_machine_data === false) && (
            <JdConnectPanel isMobile={isMobile} restrictFarmerIds={restrictFarmerIds}
              what={planting ? "planting records" : "harvest records"} />
          )}
          {!farms.length && !!untrackedFarms.length && isPortal && (
            <JdConnectPanel isMobile={isMobile} restrictFarmerIds={restrictFarmerIds}
              what={planting ? "planting records" : "harvest records"} />
          )}
          {!farms.length && !untrackedFarms.length && isPortal && (
            <JdConnectPanel isMobile={isMobile} restrictFarmerIds={restrictFarmerIds}
              what={planting ? "planting records" : "harvest records"}
              connectedTitle={planting ? "No planting recorded yet" : "No harvest recorded yet"}
              connectedNote={`Your John Deere connection is live${cropFilter ? ` — but nothing has come across for ${cropFilter.toLowerCase()}` : ""}. Acres appear here${year ? ` for ${year}` : ""} as your machines report to Operations Center, usually within a day of running.`} />
          )}
          {!farms.length && !untrackedFarms.length && !isPortal && (
            <div style={{ ...card, textAlign: "center", padding: "40px 20px", color: "#60735f", fontSize: 13, lineHeight: 1.5 }}>
              {planting
                ? <>No farms tracked for {cropFilter || "planting"} yet. Connect a farm in Ops Center, or use <b>+ Add a farm to track</b> above to add an insured grower and send them the planting survey.</>
                : <>No connected operations have keyed insured {cropFilter || "fields"} for harvest yet — connect a farm in Ops Center and finalize acreage to see it here.</>}
            </div>
          )}

          {/* Per-operation cards — one progress bar per farm for the selected mode + crop */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ranked.map(f => {
              const rowKey = f.key || f.farmer_id;
              let ins = 0, d = 0; (f.areas || []).forEach(a => { ins += denAc(a); d += doneAc(a); });
              const opPace = ins > 0 ? Math.min(1, d / ins) : 0;
              const done = cardDone(f);
              const claimN = planting ? 0 : (f.areas || []).filter(a => a.likely_claim).length;
              const reviewN = planting ? 0 : (f.areas || []).filter(a => a.missing_data_acres >= 5).length;
              const lastDate = planting ? f.last_plant_date : f.last_harvest_date;
              const daysSince = planting ? f.days_since_plant : f.days_since_harvest;
              // THE BADGE SAYS "Connected via John Deere Operations Center", so it has
              // to be read from the connection list — not assumed. It used to be
              // hardcoded true on the harvest tab ("harvest rows are all JD machine"),
              // which stamped the claim on any farm that reached that list by another
              // route. Pagen Farms carried it on 2026-08-15 with no jd_connections row,
              // no org-group membership and zero machine data of any kind.
              const jdOps = connectedIds.has(f.farmer_id);
              // Same fact the badge above was corrected for on 2026-08-15 (Pagen
              // Farms), one level out: zero machine data of any kind.
              const noMachine = f.has_machine_data === false;
              const surveyOnly = planting && f.survey && !(f.areas || []).some(a => a.src === "jd");
              const isOpen = expanded.has(rowKey);
              // Agency-only, harvest-only: one exclude control per crop on the card, with the
              // display-only "no harvest data arriving?" hint beside it. Never auto-excludes.
              const fpExCrops = (!planting && !isPortal && f.farmer_id && !noMachine)
                ? [...new Set((f.areas || []).map(a => a.crop).filter(Boolean))].map(c => ({ crop: c,
                    hint: noHarvestDataHint({ crop: c, allAreas: allAreasByFarm.get(`${f.farmer_id}|${f.year}`) || f.areas, lastHarvestDate: f.last_harvest_date, daysSinceHarvest: f.days_since_harvest }) }))
                : [];
              const fpHinted = fpExCrops.some(x => x.hint);
              return (
                <div key={rowKey} style={{ ...card, padding: 0, overflow: "hidden", borderColor: claimN > 0 ? "#e6b8ae" : "#dde7dd" }}>
                  <div onClick={() => toggle(rowKey)} style={{ display: "flex", alignItems: "center", gap: 12, padding: isMobile ? "12px 14px" : "14px 18px", cursor: "pointer", background: claimN > 0 ? "#fdf3f1" : "#fff", flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0, flex: "1 1 200px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: "#1a2e1a", fontFamily: "'Lora',serif" }}>{f.farmer}</span>
                        {jdOps && <span title="Connected via John Deere Operations Center" style={{ fontSize: 10.5, fontWeight: 800, color: "#2d6a2d", background: "#e5f3df", border: "1px solid #b8d8b0", borderRadius: 6, padding: "1px 6px", letterSpacing: "0.03em" }}>🚜 John Deere</span>}
                        {planting && f.survey && <span title="In the planting survey" style={{ fontSize: 10.5, fontWeight: 800, color: "#1a5a7a", background: "#e6f0f7", border: "1px solid #b0cfe0", borderRadius: 6, padding: "1px 6px", letterSpacing: "0.03em" }}>📋 SURVEY</span>}
                        {surveyOnly && !jdOps && <span title="Pace from the farmer's survey submissions" style={{ fontSize: 10.5, fontWeight: 800, color: "#7a5a00", background: "#fff3d9", border: "1px solid #e8d29a", borderRadius: 6, padding: "1px 6px", letterSpacing: "0.03em" }}>SELF-REPORTED</span>}
                        {fpHinted && <span data-fp-hint="1" title={fpExCrops.filter(x => x.hint).map(x => x.hint.reason).join("; ")} style={{ fontSize: 10.5, fontWeight: 800, color: "#7a5a00", background: "#fff8e8", border: "1px solid #e8d9ae", borderRadius: 6, padding: "1px 6px" }}>No harvest data arriving?</span>}
                      </div>
                      <div style={{ fontSize: 11.5, color: "#60735f", marginTop: 1 }}>
                        {f.year ? `${f.year} · ` : ""}
                        {planting
                          ? (ins > 0 ? `${Math.round(d)}/${Math.round(ins)} ac${cropFilter ? ` ${cropFilter.toLowerCase()}` : ""} planted` : `${Math.round(d)} ac planted · no baseline`)
                          : noMachine ? `${Math.round(ins)} insured ac${cropFilter ? ` ${cropFilter.toLowerCase()}` : ""} · awaiting machine data`
                          : `${Math.round(d)}/${Math.round(ins)} ac${cropFilter ? ` ${cropFilter.toLowerCase()}` : ""}`}
                        {lastDate ? ` · last ${verb.toLowerCase()} ${lastDate}${daysSince != null ? ` (${daysSince}d ago)` : ""}` : ""}
                      </div>
                    </div>
                    <div style={{ flex: "1 1 170px", minWidth: 130 }}>
                      {/* No machine data means no pace to draw. A 0% bar here is a
                          claim about the crop; this is a statement about our data. */}
                      {noMachine ? (
                        <div style={{ fontSize: 11, color: "#677167", fontStyle: "italic" }}>
                          No machine data{ins > 0 ? ` · ${Math.round(ins)} insured ac` : ""}
                        </div>
                      ) : (<>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "#60735f", marginBottom: 3 }}>
                          <span>{done ? "✓ Complete" : "In progress"}</span><span style={{ fontWeight: 800 }}>{pct(opPace)}%</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 4, background: "#eef2ee", overflow: "hidden" }}>
                          <div style={{ width: `${Math.min(100, pct(opPace))}%`, height: 8, background: barColor(done) }} />
                        </div>
                      </>)}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      {claimN > 0 && <span style={{ fontSize: 11.5, fontWeight: 800, color: "#fff", background: "#b13a24", borderRadius: 8, padding: "3px 10px" }}>⚑ {claimN} claim{claimN > 1 ? "s" : ""}</span>}
                      {reviewN > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: "#8a5a00", background: "#fff3d9", borderRadius: 8, padding: "3px 10px" }}>⚠ {reviewN}</span>}
                      <span style={{ fontSize: 16, color: "#677167" }}>{isOpen ? "▾" : "▸"}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ borderTop: "1px solid #eef2ee", padding: "8px 14px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {fpExCrops.map(({ crop, hint }) => {
                        const exKey = paceExclusionKey(f.farmer_id, f.year, crop);
                        return (
                          <div key={`ex-${crop}`} data-fp-exclude="1" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: hint ? "#fff8e8" : "#f6f8f6", border: `1px solid ${hint ? "#e8d9ae" : "#e3e9e3"}`, borderRadius: 8, padding: "7px 10px" }}>
                            <span style={{ flex: "1 1 200px", minWidth: 0, fontSize: 11.5, color: hint ? "#7a5c00" : "#60735f" }}>
                              {hint ? <><b>No harvest data arriving?</b> {hint.reason}.</> : <>Not getting {String(crop).toLowerCase()} harvest data for this farm?</>}
                            </span>
                            <button onClick={(e) => { e.stopPropagation(); setPaceExclusion(f, crop, true); }} disabled={savingExclusion === exKey}
                              style={{ cursor: "pointer", border: "1px solid #b8a06a", background: "#fff", color: "#6a4a10", borderRadius: 6, padding: "6px 11px", fontSize: 11.5, fontWeight: 800 }}>
                              {savingExclusion === exKey ? "Saving…" : `Exclude ${String(crop).toLowerCase()} from harvest pace`}</button>
                          </div>
                        );
                      })}
                      {/* The survey token is a credential for an UNAUTHENTICATED
                          page — never rendered on a portal screen. */}
                      {planting && !isPortal && f.survey && f.token && (() => {
                        const link = `${window.location.origin}/planting/${f.token}`;
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: "#f4f8fb", border: "1px solid #d4e4f0", borderRadius: 8, padding: "7px 10px" }}>
                            <span style={{ fontSize: 11, fontWeight: 800, color: "#1a5a7a" }}>📋 Survey link</span>
                            <span style={{ fontSize: 11, color: "#4a6e7a", fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis" }}>{link}</span>
                            <button onClick={() => { try { navigator.clipboard.writeText(link); } catch (e) {} }} style={{ marginLeft: "auto", cursor: "pointer", border: "none", background: "#1a5a7a", color: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700 }}>Copy</button>
                          </div>
                        );
                      })()}
                      {(f.areas || []).map((a, i) => {
                        const aDone = areaComplete(a);
                        const aPace = denAc(a) > 0 ? Math.min(1, doneAc(a) / denAc(a)) : 0;
                        return (
                        <div key={i} style={{ border: `1px solid ${a.likely_claim && !planting ? "#e6b8ae" : aDone ? "#cfe3ca" : "#e3e9e3"}`, borderRadius: 10, padding: "9px 12px", background: a.likely_claim && !planting ? "#fdf3f1" : "#fafcfa" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 800, fontSize: 13, color: CROP_COLOR[a.crop] || "#1a2e1a" }}>{a.crop}</span>
                            <span style={{ fontSize: 12, color: "#4a6e4a" }}>{a.county}</span>
                            {aDone && !areaAcresShort(a)
                              ? <span style={{ fontSize: 10, fontWeight: 800, color: "#2d6a2d", background: "#e5f3df", borderRadius: 8, padding: "2px 8px" }}>✓ ALL IN</span>
                              : aDone && areaAcresShort(a)
                              /* Every field is in and the acres are not. Saying so takes BOTH
                                 halves: a field count alone reads as finished, an acre figure
                                 alone reads as a farm still going. */
                              ? <span title="Every field has a harvest pass, but the machine acres fall short of the insured acres by more than normal headland trim." style={{ fontSize: 10, fontWeight: 800, color: "#8a5a00", background: "#fff3d9", borderRadius: 8, padding: "2px 8px" }}>{areaFieldsDone(a) != null ? `${areaFieldsDone(a)}/${a.fields_total} fields` : "FIELDS IN"} · {pct(a.acre_coverage || 0)}% OF ACRES</span>
                              : <span style={{ fontSize: 10, fontWeight: 800, color: "#8a5a00", background: "#fff3d9", borderRadius: 8, padding: "2px 8px" }}>{areaFieldsDone(a) != null ? `${areaFieldsDone(a)}/${a.fields_total} fields` : `${pct(areaCompleteness(a))}%`}</span>}
                            <span style={{ marginLeft: "auto", fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 12.5, color: barColor(aDone) }}>{pct(aPace)}%</span>
                          </div>
                          <div style={{ height: 6, borderRadius: 3, background: "#eef2ee", overflow: "hidden", margin: "7px 0" }}>
                            <div style={{ width: `${Math.min(100, pct(aPace))}%`, height: 6, background: barColor(aDone) }} />
                          </div>
                          {planting ? (<>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: "#3a4f3a", alignItems: "center" }}>
                              <span>Planted <b>{doneAc(a).toFixed(1)}</b> ac</span>
                              <span>Baseline <b>{a.denom != null ? a.denom : "—"}</b> ac <span style={{ fontSize: 10, color: "#60735f" }}>({SRC_LABEL[a.denom_source] || a.denom_source})</span></span>
                              <span style={{ fontSize: 10, color: "#60735f" }}>{a.src === "survey" ? `self-reported${a.last_submit ? ` ${String(a.last_submit).slice(0, 10)}` : ""}` : "machine"}</span>
                              {(a.by_county || []).length > 1 && <span style={{ color: "#60735f" }}>{(a.by_county || []).map(bc => `${bc.county} ${Math.round(bc.planted_acres)}`).join(" · ")}</span>}
                            </div>
                            {/* Intended acres is the AGENT's baseline override
                                (planting_intended_acres, admin-write). A farmer
                                reads the baseline above; they don't set it. */}
                            {!isPortal && (
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 11, color: "#60735f", fontWeight: 700 }}>Intended acres</span>
                              <input type="number" defaultValue={a.intended_acres ?? ""} placeholder={a.prior_insured ? `${Math.round(a.prior_insured)} (last yr)` : "acres"}
                                onKeyDown={e => { if (e.key === "Enter") saveIntended(f, a.crop, e.target.value); }}
                                style={{ width: 100, fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid #cdd9cd" }} />
                              <button onClick={e => saveIntended(f, a.crop, e.currentTarget.previousSibling.value)}
                                disabled={savingIntended === `${f.key || f.farmer_id}|${a.crop}`}
                                style={{ fontSize: 11.5, fontWeight: 700, padding: "5px 12px", borderRadius: 6, border: "none", background: "#2d4a2d", color: "#fff", cursor: "pointer" }}>
                                {savingIntended === `${f.key || f.farmer_id}|${a.crop}` ? "Saving…" : "Set"}</button>
                              {a.intended_acres != null && <span style={{ fontSize: 10.5, color: "#2d6a2d", fontWeight: 700 }}>✓ intended {Math.round(a.intended_acres)} ac</span>}
                              {a.intended_acres == null && a.denom_source === "prior-year" && <span style={{ fontSize: 10.5, color: "#677167" }}>using last year until set</span>}
                            </div>
                            )}
                          </>) : (<>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: "#3a4f3a" }}>
                              <span>Planted <b>{fpHarvestDen(a).toFixed(1)}</b> ac</span>
                              <span>Harvested <b>{doneAc(a).toFixed(1)}</b> ac</span>
                              {a.custom_harvested_acres ? <span style={{ color: "#2d6a2d" }}>incl. <b>{a.custom_harvested_acres}</b> ac custom-harvested{a.custom_from ? ` by ${a.custom_from}` : ""}</span> : null}
                              {a.yield != null && <span>Yield <b style={{ color: a.likely_claim ? "#b13a24" : "#1a4a1a" }}>{a.yield}</b> {watchUnit(a)}</span>}
                              {a.guarantee_yield != null && <span>Guar <b>{a.guarantee_yield}</b> {watchUnit(a)}</span>}
                              {/* Chopped ground reports on its own axis. Kevin, 2026-10-05:
                                  "even though we use appraisials, id still like to see the
                                  silage data." The unit is spelled out on every figure — a
                                  bare "6.5" beside a bushel guarantee is the conflation. */}
                              {a.silage_yield != null && <span>Silage <b style={{ color: "#8a5a2a" }}>{a.silage_yield}</b> {silageUnitLabel(a.silage_unit)}</span>}
                              {a.silage_guarantee != null && <span>Silage guar <b>{a.silage_guarantee}</b> ton/ac</span>}
                              {a.likely_claim && a.shortfall_bu > 0 && <span style={{ color: "#b13a24", fontWeight: 700 }}>Short {a.shortfall_bu.toLocaleString()} {watchTotalUnit(a)}</span>}
                              {a.likely_claim && <span title="Machine-yield screener: John Deere yield against the planted-acre guarantee, before tickets exist. Not a claim estimate." style={{ fontWeight: 800, color: "#b13a24" }}>⚑ LIKELY CLAIM{a.est_indemnity ? ` ~$${a.est_indemnity.toLocaleString()}` : ""} · machine-yield screener</span>}
                              {a.likely_claim && a.claim_partial_basis && <span title="The yield behind this read was measured on part of the ground and applied to every insured acre." style={{ fontSize: 10.5, fontWeight: 700, color: "#7a5c00", background: "#fff8e8", border: "1px solid #e8d9ae", borderRadius: 6, padding: "1px 6px", marginLeft: 6 }}>on {Math.round((a.yield_sample_share || 0) * 100)}% of the acres</span>}
                            </div>
                            {/* Agent-only QC note. It tells the AGENT to go chase
                                the missing machine data before reading a claim off
                                this row — an internal to-do about OUR data coverage,
                                not a fact about the grower's crop. A farmer reading
                                "chase before trusting a claim read" on their own page
                                has nothing to act on and every reason to distrust the
                                number above it. Hidden in portal 2026-08-13. */}
                            {/* ── WHAT THE ACRE GAP IS, WHEN THERE IS ONE ──────────────────
                                Kevin, 2026-10-17, on Panek Farms Niagara reading 188 insured
                                / 176.4 harvested under a green badge: "I just want to make
                                sure we don't fall into a trap with future farms where they
                                truly are short on acres and we somehow flag it as all in".

                                The gap was never explained on a county that had nothing else
                                to report. The card carries notes for ground harvested but not
                                insured, and for insured ground with no machine data — so a
                                county with NEITHER got no note at all and the reader was left
                                with a dozen unexplained acres beside the word ALL IN.

                                Both states are named now, and they are different facts: within
                                normal trim it says so, and below the measured floor it says the
                                acres do not back the badge. Rendered only where machine coverage
                                is actually short — an overrun is already unmatched_acres. */}
                            {!planting && a.acre_gap_acres > 0 && a.fields_total != null && (
                              <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5, color: areaAcresShort(a) ? "#7a5c00" : "#4a5a4a", background: areaAcresShort(a) ? "#fff8e8" : "#f4f8f4", border: "1px solid " + (areaAcresShort(a) ? "#e8d9ae" : "#e4ece4"), borderRadius: 6, padding: "5px 8px" }}>
                                {areaAcresShort(a) ? "⚠ " : "📐 "}
                                <b>{a.acre_gap_acres}</b> ac of the planted acreage carries no machine pass
                                {areaAcresShort(a)
                                  ? <> — that is <b>{pct(1 - (a.acre_coverage || 0))}%</b>, more than headland trim accounts for, so every field having a pass does <b>not</b> mean the acres are all in. Check the fields below before reading this county as finished.</>
                                  : (a.blocking_fields || []).length > 0
                                  /* ⚠ A BIG FIELD WITH NO PASS IS NAMED, and the "every field" sentence
                                     cannot render beside it (Silsby Niagara soybeans, 2026-10-20). */
                                  ? <> — not done: {(a.blocking_fields || []).map((bf) => fpBlockingFieldLabel(bf)).join("; ")}.</>
                                  : a.fields_harvested != null && a.fields_harvested === a.fields_total
                                  ? <> — headlands, waterways and GPS edges, which is the usual <b>3–7%</b>. Every insured field has a harvest pass, so the acres are accounted for.</>
                                  : <> — some insured fields have no harvest pass yet.</>}
                                {(a.short_fields || []).length > 0 && (
                                  <div style={{ marginTop: 4 }}>
                                    Thinnest: {(a.short_fields || []).slice(0, 3).map((f, k) => (
                                      <span key={k} style={{ marginRight: 10, fontVariantNumeric: "tabular-nums" }}>
                                        <b>{[f.fsn, f.tract, f.field].filter(Boolean).join("/")}</b> {f.harvested_acres}/{f.insured_acres} ac ({pct(f.coverage)}%)
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {/* Same rule as Claim Watch: a check that could not run says so. */}
                            {!isPortal && a.missing_data_acres == null && (
                              <div style={{ marginTop: 6, fontSize: 11, color: "#5a5a6a", background: "#f4f5f8", border: "1px solid #dfe2ea", borderRadius: 6, padding: "5px 8px" }}>
                                🔎 County-level read — the missing-machine-data check needs acreage keyed to CLUs. Not measured is not zero.
                              </div>
                            )}
                            {/* ⚠⚠ THE ACRES THAT USED TO VANISH. `uninsured_clu_acres` has
                                been computed and returned since the partition comment above
                                it was written — naming Sullivan's 25.0 ac by number — and
                                was rendered NOWHERE. The backend was fixed and the screen
                                never got it, which is the "looks wired and isn't" class.

                                Kevin, 2026-09-29: *"Sullivan Farms is still low on harvested
                                dry beans acres compared to whats in his JD Ops account."*
                                He was reading 119.6 ac in JD against 85.4 here. Traced: 9.2
                                ac lost to headland/GPS edges in CLU attribution, and 25.0 ac
                                harvested on Farm 7565 / Tract 5214 — which NAU's own file
                                carries no insurable acreage line for, because they are SMALL
                                REDS and small reds are uninsurable (Kevin, same day; the
                                acreage report shows that unit as `-UI` with a blank APH).

                                So the 85.4 was RIGHT and the silence was the defect. Three
                                figures now partition the cell's machine acres — harvested +
                                this + unmatched — so a reader can add them back up to what
                                JD shows instead of concluding we are under-counting.

                                ⚠ IT NAMES NO CAUSE. Uninsurable type, another crop's ground
                                and custom work all land here and this page cannot tell them
                                apart; saying "uninsurable" would be right for Sullivan and
                                invented for the next farm. Same floor as the sibling line so
                                a headland sliver does not raise a note. */}
                            {!isPortal && a.uninsured_clu_acres >= 5 && (
                              <div style={{ marginTop: 7, fontSize: 11, color: "#3a5a6a", background: "#eff6f9", border: "1px solid #cfe0ea", borderRadius: 7, padding: "5px 9px" }}>
                                🚜 {a.uninsured_clu_acres} ac of {a.crop} were harvested on ground <b>not insured for
                                this crop</b> in {a.county}{a.uninsured_clus ? ` (${a.uninsured_clus} field${a.uninsured_clus === 1 ? "" : "s"})` : ""} —
                                an uninsurable type, another crop's ground, or custom work. Real acres, deliberately
                                outside the {a.harvested_acres} ac above, which counts insured ground only. Together
                                they are ~{(+a.harvested_acres + +a.uninsured_clu_acres).toFixed(1)} ac of CLU-matched
                                machine data; John Deere's own field totals run a few percent higher again, because
                                matching a pass to a CLU trims headlands and GPS edges.
                              </div>
                            )}
                            {/* ⚠ THE BLANK YIELD CELL HAD NO EXPLANATION, AND IT READ AS A
                                BROKEN PIPELINE. Kevin, 2026-10-05: *"why is Red Dawn not
                                showing yields in harvested progress"*. He had 2,445 harvested
                                acres and an empty bushel cell, because every one of those
                                acres came off as SILAGE: tons, against a bu/ac guarantee.
                                The acres fold (the ground was harvested) and the production
                                deliberately does not — so the only thing missing was the
                                sentence saying so. A blank cell must say why. */}
                            {!isPortal && a.silage_acres >= 5 && (
                              <div style={{ marginTop: 7, fontSize: 11, color: "#6a4a2a", background: "#fbf4ec", border: "1px solid #ecd9b8", borderRadius: 7, padding: "5px 9px" }}>
                                🌽 {a.silage_acres} ac of this {a.crop} came off as <b>silage</b>
                                {a.silage_yield != null ? <> — <b>{a.silage_yield}</b> {silageUnitLabel(a.silage_unit)}{a.silage_measured_acres && a.silage_measured_acres < a.silage_acres ? " over " + a.silage_measured_acres + " measured ac" : ""}{a.silage_tons ? ", ~" + a.silage_tons.toLocaleString() + " ton" : ""}</> : " — no usable monitor yield on those passes"}.
                                Those acres count as harvested above; their production is <b>not</b> in the bushel
                                figure, because tons and bushels are not the same quantity.
                                {a.silage_guarantee == null
                                  ? " This policy insures the corn as GRAIN, with no silage line, so production to count on chopped acres comes from the adjuster's appraisal — not the monitor."
                                  : " The silage line's own guarantee is shown beside it."}
                              </div>
                            )}
                            {!isPortal && a.missing_data_acres >= 5 && (
                              <div style={{ marginTop: 7, fontSize: 11, color: "#6a5a2a", background: "#fbf7ec", border: "1px solid #ecdfb8", borderRadius: 7, padding: "5px 9px" }}>
                                📭 {a.missing_data_acres} ac of insured {a.crop} have no machine data yet — {a.pipeline_pending_ops > 0 ? "ours: " + a.pipeline_pending_ops + " harvest op(s) un-mapped, recompute first." : "chase before trusting a claim read."}
                              </div>
                            )}
                          </>)}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* ⚠ EXCLUDED IS LISTED, NEVER DROPPED — greyed, with what it took out of the pace
              and a one-click re-include. Agency only (excludedFarms is empty in the portal). */}
          {!planting && !isPortal && excludedFarms.length > 0 && (
            <div data-fp-excluded="1" style={{ ...card, padding: 0, marginTop: 12, background: "#f6f7f6", opacity: 0.92 }}>
              <div style={{ padding: isMobile ? "12px 14px" : "12px 18px" }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: "#4a554a" }}>
                  Excluded from pace — no harvest data ({excludedSummary.farms} {excludedSummary.farms === 1 ? "farm" : "farms"}, {fmt(excludedSummary.acres, 0)} planted ac)
                </div>
                <div style={{ fontSize: 11.5, color: "#677167", marginTop: 2 }}>
                  Set by an agent. These acres are left out of the pace (both the harvested and the planted side); likely claims still count.
                </div>
              </div>
              <div style={{ borderTop: "1px solid #e6ebe6", padding: "6px 14px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
                {[...excludedFarms].sort((a, b) => (b.planted_acres || 0) - (a.planted_acres || 0)).map(f => (
                  <div key={`ex-${f.key || f.farmer_id}-${f.year}`} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "#5d665d", borderBottom: "1px dashed #e6ebe6", padding: "6px 0" }}>
                    <span style={{ fontWeight: 800, color: "#4a554a", flex: "1 1 180px", minWidth: 0 }}>{f.farmer}</span>
                    <span>{fmt(f.harvested_acres, 0)}/{fmt(f.planted_acres, 0)} ac · {f.crops.map(c => c.toLowerCase()).join(", ")}{f.year ? ` · ${f.year}` : ""}</span>
                    {f.crops.map(c => {
                      const exKey = paceExclusionKey(f.farmer_id, f.year, c);
                      return (
                        <button key={c} onClick={() => setPaceExclusion(f, c, false)} disabled={savingExclusion === exKey}
                          style={{ cursor: "pointer", border: "1px solid #2d4a2d", background: "#fff", color: "#2d4a2d", borderRadius: 6, padding: "6px 11px", fontSize: 11.5, fontWeight: 800 }}>
                          {savingExclusion === exKey ? "Saving…" : `Re-include${f.crops.length > 1 ? ` ${c.toLowerCase()}` : ""}`}</button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* ⚠ NOT TRACKED IS LISTED, NEVER DROPPED. These farms have no machine source
              for the crop (no John Deere connection, and no material planting or harvest of
              it on their insured ground), so they sit outside the pace and To review. */}
          {!planting && untrackedFarms.length > 0 && (
            <div data-fp-untracked="1" style={{ ...card, padding: 0, marginTop: 12, background: "#fafbfa" }}>
              <div onClick={() => setShowUntracked(v => !v)} style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "12px 18px", cursor: "pointer", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: "#3d4a3d" }}>
                    Not tracked — no machine data source ({untrackedFarms.length} {untrackedFarms.length === 1 ? "farm" : "farms"}, {fmt(totals.untrackedInsured, 0)} insured ac)
                  </div>
                  <div style={{ fontSize: 11.5, color: "#677167", marginTop: 2 }}>
                    Left out of the pace{showClaims ? " and To review" : ""}: no John Deere connection, and no real planting or harvest of the crop on the insured ground.
                  </div>
                </div>
                <span style={{ fontSize: 16, color: "#677167" }}>{showUntracked ? "▾" : "▸"}</span>
              </div>
              {showUntracked && (
                <div style={{ borderTop: "1px solid #eef2ee", padding: "6px 14px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
                  {[...untrackedFarms].sort((a, b) => (b.insured_acres || 0) - (a.insured_acres || 0)).map(f => (
                    <div key={`ut-${f.key || f.farmer_id}`} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: 12, borderBottom: "1px dashed #eef2ee", padding: "5px 0" }}>
                      <span style={{ fontWeight: 800, color: "#1a2e1a", flex: "1 1 180px", minWidth: 0 }}>{f.farmer}</span>
                      <span style={{ color: "#3d4a3d", fontWeight: 700 }}>{fmt(f.insured_acres, 0)} insured ac{(() => { const cs = [...new Set((f.areas || []).map(a => String(a.crop || "").toLowerCase()).filter(Boolean))]; return cs.length ? ` · ${cs.join(", ")}` : ""; })()}</span>
                      <span style={{ flexBasis: "100%", color: "#677167", fontSize: 11.5 }}>{f.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FarmerView({ farmer, prices, section, onUpdate, onBack, onReset, isMobile, onOpenFields, onOpenMarket, lobFilter = null, priceMapForYear = null, customerView = false }) {
  // Book-data freshness — LRP/DRP/Spring/Fall/Claims all render policy rows
  // owned by the daily M13/NAU sync. One "book synced {date}" on the header
  // covers every sub-tab (PRF shows its own rainfall freshness inside PRFView).
  const [bookSynced, setBookSynced] = useState(null);
  // PRF sub-tab. The two PRF screens were two nav items answering two different
  // questions about one policy — what the RAIN did, and what was BOUGHT — while
  // repeating the policy header between them. Kevin, 2026-09-07: "I think thats an
  // unneccesary overlap". They are one page with two tabs now.
  //
  // Rainfall leads because it is the question with a live answer: an interval either
  // triggered or it did not, and that changes every month. What the grower bought
  // changes once a year.
  const [prfTab, setPrfTab] = useState("rainfall");
  const [showFieldData, setShowFieldData] = useState(false);   // John Deere machine-data table overlay
  const [jdFarmerId, setJdFarmerId] = useState(null);          // the DB farmer_id that's Ops-Center-connected
  const jdConnected = !!jdFarmerId;

  // ── Agent of Record editor ──────────────────────────────────────
  // agent_name is sync-owned (P55 roster), so a manual AOR is stored in the
  // separate agent_name_override column (by policy_number, current crop year) that
  // the sync never touches. Local state reflects the edit immediately.
  const [agentLocal, setAgentLocal]   = useState(null);   // null = show loader value
  const [editingAgent, setEditingAgent] = useState(false);
  const [savingAgent, setSavingAgent]   = useState(false);
  useEffect(() => { setAgentLocal(null); setEditingAgent(false); }, [farmer.id]);
  const displayAgent = agentLocal != null ? agentLocal : (farmer._agentOverride || farmer.agent || "");
  const saveAgentOverride = async (value) => {
    // value === "" clears the override (falls back to the synced agent_name).
    const nums = (farmer._allPolicyNums && farmer._allPolicyNums.length
      ? farmer._allPolicyNums
      : String(farmer.policyNumber || "").split(" · ")).map(s => s.trim()).filter(Boolean);
    if (!nums.length) { setEditingAgent(false); return; }
    setSavingAgent(true);
    try {
      const list = nums.map(n => `"${n.replace(/"/g, '')}"`).join(",");
      // Not scoped by crop_year — the AOR applies to the policy across years, and
      // it dodges the livestock-only edge case where _cropDisplayYear is 0.
      const res = await authedFetch(
        `${SUPABASE_URL}/rest/v1/policies?policy_number=in.(${encodeURIComponent(list)})`,
        { method: "PATCH", headers: { Prefer: "return=minimal" }, body: JSON.stringify({ agent_name_override: value || null }) }
      );
      if (!res.ok) throw new Error(await res.text());
      setAgentLocal(value || (farmer.agent || ""));
      farmer._agentOverride = value || undefined;   // reflect on the in-memory object
      setEditingAgent(false);
    } catch (e) { alert(`Couldn't save agent: ${String(e.message || e).slice(0, 200)}`); }
    finally { setSavingAgent(false); }
  };
  // Editing is admin-only — a portal farmer sees their agent as plain text.
  const agentEditor = !isCurrentUserAdmin() ? (
    // A customer is served by the agency, not by whichever account the policy
    // sits under ("MM KK Service Account" reached a grower's card). Kevin,
    // 2026-10-18: "They don't need to see anything — just show Clover Ag."
    <span>Agent: Clover Ag</span>
  ) : (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {editingAgent ? (
        <>
          <select defaultValue={AGENCY_AGENTS.includes(displayAgent) ? displayAgent : ""} disabled={savingAgent}
            onChange={(e) => saveAgentOverride(e.target.value)}
            style={{ fontSize: 12, padding: "2px 6px", borderRadius: 5, border: "1px solid #c8d8c8", background: "#fff", color: "#1a2e1a" }}>
            <option value="">— auto (from sync)</option>
            {AGENCY_AGENTS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={() => setEditingAgent(false)} disabled={savingAgent}
            style={{ background: "none", border: "none", color: "#677167", fontSize: 12, cursor: "pointer", padding: 0 }}>✕</button>
        </>
      ) : (
        <button onClick={() => setEditingAgent(true)} title="Edit Agent of Record"
          style={{ background: "#eef6ec", border: "1px solid #cfe6cf", borderRadius: 12, color: "#2d6a2d",
            fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "2px 9px", display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ color: "#60735f", fontWeight: 500 }}>Agent:</span> {displayAgent || "—"} <span style={{ fontSize: 12 }}>✎</span>
        </button>
      )}
    </span>
  );

  // ── Unified farmer card ────────────────────────────────────────
  // One view per farmer, with a tab per line of business they actually carry.
  // You land on the section that was clicked (the `section` prop) and switch
  // between the rest without leaving — no separate Spring / Fall / DRP pages.
  const avail = useMemo(() => {
    const ay = farmer.approvedYields || [], pe = farmer.ppEligibility || [];
    const types = farmer.policyTypes || [];
    const isFall = c => FALL_CROPS.includes((c || "").toUpperCase());
    const hasSpring = ay.some(a => a.crop && !isFall(a.crop)) || pe.some(p => p.crop && !isFall(p.crop));
    const hasFall   = ay.some(a => isFall(a.crop)) || pe.some(p => isFall(p.crop));
    // Child arrays are the primary signal; policyTypes is a fallback so a tab
    // still appears if the farmer carries the LOB but its rows loaded thin.
    const hasDrp = (farmer.drpPolicies || []).some(p => (p.endorsements || []).length) || types.includes("drp");
    const hasLrp = (farmer.lrpPolicies || []).some(p => (p.endorsements || []).length) || types.includes("lrp");
    const hasPrf = (farmer.prfPolicies || []).length > 0 || types.includes("prf");
    // ⚠ THE PILL SAYS WHETHER THE COVERAGE IS CURRENT, because it is read as an
    // answer and not as a link. Kevin, reviewing the Anchor fix: *"If Anchor still
    // has the exact same prominent PRF coverage pill as a farm with CY2026 coverage,
    // an agent could reasonably interpret the pill itself as 'this customer has
    // PRF'... can I tell before clicking?"* On the rendered card the answer was NO —
    // Anchor's pill was byte-identical to one on a farm carrying current coverage.
    //
    // Accessibility and presentation are different things: the history must stay
    // REACHABLE (so the pill remains) and must not READ as coverage in force (so it
    // is named for what is behind it). A farm with no PRF rows at all still gets no
    // pill; that branch is untouched.
    const prfCurrent = hasPrf && prfCoverageState(farmer.prfPolicies, prfCoverageYear()).hasCurrent;
    return [
      hasSpring && { key: "spring", label: "Spring Crops",   emoji: "🌱" },
      hasFall   && { key: "fall",   label: "Fall Crops",     emoji: "🌾" },
      hasDrp    && { key: "drp",    label: "Dairy (DRP)",    emoji: "🥛" },
      hasLrp    && { key: "lrp",    label: "Livestock (LRP)", emoji: "🐄" },
      hasPrf    && { key: "prf",    label: prfCurrent ? "PRF" : "PRF History",  emoji: "🌧️" },
    // Portal grants scope by farmer_id at the DB (RLS), NOT per-LOB — so honor the
    // granted `lobs` here: a scoped grantee only sees the coverage tabs they were
    // granted, even though the farmer's other-LOB rows are technically readable.
    ].filter(Boolean).filter(a => !lobFilter || lobFilter.has(a.key));
  }, [farmer, lobFilter]);
  const resolveSection = (want) => (avail.some(a => a.key === want) ? want : (avail[0]?.key || want));
  const [activeSection, setActiveSection] = useState(() => resolveSection(section));
  // Reset to the clicked section when a new farmer (or a new incoming section)
  // is opened; toggles within the same farmer persist until you navigate away.
  useEffect(() => { setActiveSection(resolveSection(section)); }, [farmer.id, section]); // eslint-disable-line

  useEffect(() => {
    let alive = true;
    // A card can span multiple DB farmer_ids (allFarmerDbIds); match the connection
    // against ANY of them, and remember which one (that's what jd-fielddata queries).
    const dbIds = farmer.allFarmerDbIds || farmer._allFarmerIds || [farmer.id].filter(Boolean);
    // Ask about THIS card's farms, not the whole roster. All this needs is
    // "is this farm connected?", and the unfiltered call returned every
    // connected customer's org name in the body — invisible on screen, but a
    // payload no portal session should ever be handed, and an admin previewing
    // the portal was getting exactly that. `?farmer=` is intersected with the
    // caller's own scope server-side, so this narrows and never widens.
    const q = dbIds.length ? `&farmer=${dbIds.map(encodeURIComponent).join(",")}` : "";
    authedFetch(`/api/market?fn=jd-connected${q}`)
      .then(r => r.json())
      .then(d => { if (alive && d && d.connections) { const m = d.connections.find(c => dbIds.includes(c.farmer_id)); setJdFarmerId(m ? m.farmer_id : null); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [farmer.id]); // eslint-disable-line
  useEffect(() => {
    let alive = true;
    // sync_freshness, not m13_import_log: the log row carries total_premium
    // (the whole book's premium per sync, $10.5M for NAU) and FarmerView is
    // portal-reachable, so every farmer login could read it. The view exposes
    // only the timestamp this pill needs. See sql/014.
    sbFetch("sync_freshness", { select: "imported_at", order: "imported_at.desc", limit: 1 })
      .then(r => { if (alive) setBookSynced(r && r[0] ? r[0].imported_at : null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const bookFresh = activeSection !== "prf" ? <FreshAsOf date={bookSynced} label="book" prefix="synced" /> : null;
  // Field Data + Fields action buttons — rendered inline in each section's tab
  // row (right after Prevent Plant). They share the tab row's underline format
  // for cohesion but stay a clearly-clickable green (they OPEN tools, they aren't
  // content tabs), separated by a thin divider so the tools cluster reads apart
  // from the content tabs. Farmer-level, passed down as a node to Spring/Fall.
  const fieldActionTab = {
    padding: isMobile ? "8px 10px" : "10px 16px", border:"none", background:"none", cursor:"pointer",
    borderBottom:"3px solid transparent", color:"#2d6a2d", fontWeight:700,
    fontSize: isMobile ? 12 : 14, marginBottom:-1, whiteSpace:"nowrap",
    display:"inline-flex", alignItems:"center", gap:6, fontFamily:"inherit",
  };
  const fieldActions = (jdConnected || onOpenFields) ? (
    <>
      <span aria-hidden style={{ alignSelf:"center", width:1, height:20, background:"#dbe6db", margin:"0 4px" }} />
      {jdConnected && (
        <button onClick={() => setShowFieldData(true)} title="John Deere machine data — planting & yield by field / county, vs FSA keyed acres" style={fieldActionTab}>
          📊 Field Data
        </button>
      )}
      {onOpenFields && (
        <button onClick={onOpenFields} title="View this farmer's fields in the Field Mapper" style={fieldActionTab}>
          🌾 Fields
        </button>
      )}
    </>
  ) : null;
  return (
    <div>
      {/* Back names where it goes: the farmer's own coverage page in the portal, the
          farm's page for an agent — never "Map", with no map behind it. */}
      {showFieldData && <JdFieldDataView farmer={farmer} farmerId={jdFarmerId} onClose={() => setShowFieldData(false)}
        backLabel={isCurrentUserAdmin() ? (farmer.name || "Back") : "My coverage"} />}
      {/* ── THE FARM HEADER ────────────────────────────────────────────────
          One card for both widths: a dark band carrying who this is and what they
          carry, and a light strip carrying how to reach them and where the record
          comes from. The same component is the customer's own My Coverage, so the
          band is the first thing a grower sees about their own farm. */}
      <header style={{ borderRadius: isMobile ? 18 : 20, overflow: "hidden", marginBottom: isMobile ? 16 : 22,
                       border: "1px solid #dbe6da", background: "#fff", boxShadow: "0 1px 2px rgba(18,36,26,0.04)" }}>
        <div style={{ background: "#12241a", color: "#f4f8f1", padding: isMobile ? "16px 16px 18px" : "20px 26px 24px",
                      display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ flex: "1 1 360px", minWidth: 0 }}>
            {/* ⚠ NO HANDLER, NO BUTTON. A "← BACK" that is rendered unconditionally is
                offered even when the caller has nowhere to send the reader — which is
                exactly how it came to be clickable and dead in the portal. The two
                MOBILE back buttons in the portal shell already gate on `selectedFarmer`
                and were correct throughout; this one did not, and was the one Mike
                found. Callers that can go back pass a handler; callers that cannot
                pass null. */}
            {!isMobile && onBack && (
              <button onClick={onBack} style={{ background: "none", border: "1px solid #3c5a43", borderRadius: 999, padding: "6px 14px",
                cursor: "pointer", color: "#c7d8c3", fontSize: 12.5, fontWeight: 700, marginBottom: 12, fontFamily: "inherit" }}>← Back</button>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              {/* No farmer-wide crop-year badge: a farmer's products span years
                  (livestock rolls July 1, crops/PRF don't), so one number is
                  always wrong for some screen. Each product view shows its own
                  year inline instead. */}
              {farmer.policyTypes?.map((t) => <TypeBadge key={t} type={t} />)}
              {jdConnected && <span title="Connected to John Deere Operations Center" style={{ background: "#367c2b", color: "#fff", fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 12, letterSpacing: "0.04em" }}>🚜 John Deere</span>}
            </div>
            <h1 style={{ margin: 0, fontFamily: "'Lora',serif", fontWeight: 600, fontSize: isMobile ? 26 : 34,
                         lineHeight: 1.12, letterSpacing: "-0.01em", color: "#f4f8f1" }}>{farmer.name}</h1>
          </div>
          <button
            onClick={() => generatePolicyPDF(farmer, activeSection, prices, priceMapForYear)}
            style={{ background: "#a9d38a", color: "#12241a", border: "none", borderRadius: 999, padding: "11px 18px", cursor: "pointer",
                     fontSize: 13.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                     minHeight: 42, fontFamily: "inherit", flexShrink: 0, width: isMobile ? "100%" : "auto" }}>
            🖨 Export PDF
          </button>
          {/* The lender copy of the whole farm. Agents only: it carries ARC/PLC and SCO/ECO projections. */}
          {isCurrentUserAdmin() && (
            <button
              onClick={() => exportFarmStatement(farmer)}
              title="Coverage in force, insurance paid this year and estimated payments, for a lender"
              style={{ background: "transparent", color: "#d6ebc8", border: "1.5px solid #a9d38a", borderRadius: 999, padding: "10px 16px", cursor: "pointer",
                       fontSize: 13.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                       minHeight: 42, fontFamily: "inherit", flexShrink: 0, width: isMobile ? "100%" : "auto" }}>
              Farm statement
            </button>
          )}
        </div>
        <div style={{ padding: isMobile ? "12px 16px" : "14px 26px", display: "flex", flexWrap: "wrap", gap: "8px 18px",
                      alignItems: "center", fontSize: 13, color: "#4f6a4f" }}>
          {/* Each detail is its own item and the gap separates them — no "•" glyphs,
              so an empty insurer (most cards) cannot leave a dangling separator. */}
          <React.Fragment key="ag">{agentEditor}</React.Fragment>
          {farmer.phone ? <span key="ph" style={{ fontWeight: 600, color: "#2d4a2d" }}>{farmer.phone}</span> : null}
          {farmer.address && !isMobile ? <span key="ad">{farmer.address}</span> : null}
          {farmer.insurer ? <span key="ins">{farmer.insurer}</span> : null}
          {farmer.policyNumber ? <span key="pn" style={{ fontVariantNumeric: "tabular-nums", color: "#60735f" }}>{farmer.policyNumber}</span> : null}
          {bookFresh ? <React.Fragment key="bf">{bookFresh}</React.Fragment> : null}
        </div>
      </header>
      {/* Coverage switcher — one prominent pill per line of business this farmer
          carries, so it's obvious how to move between their coverages. */}
      {avail.length > 1 && (
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:22 }}>
          {avail.map(t => {
            const on = activeSection === t.key;
            return (
              <button key={t.key} onClick={() => setActiveSection(t.key)}
                style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontFamily:"inherit",
                  padding: isMobile ? "10px 15px" : "12px 22px", borderRadius:11,
                  border: on ? "2px solid #2d6a2d" : "2px solid #dde7dd",
                  background: on ? "#2d6a2d" : "#fff",
                  color: on ? "#fff" : "#4a6e4a",
                  fontSize: isMobile ? 14 : 16, fontWeight: on ? 800 : 700, letterSpacing:"0.01em",
                  boxShadow: on ? "0 3px 10px rgba(45,106,45,0.28)" : "none", transition:"all 0.12s" }}>
                <span style={{ fontSize: isMobile ? 17 : 19 }}>{t.emoji}</span>{isMobile ? t.label.replace(/ .*/, "") : t.label}
              </button>
            );
          })}
        </div>
      )}
      {/* Coverage → tools link. The card shows the coverage; quoting + market
          intelligence + everything else live on the dedicated DRP/LRP/PRF page.
          One click from "my protection" to the full toolset for that coverage. */}
      {onOpenMarket && ({ drp: "DRP quoting & market", lrp: "LRP quoting & market", prf: "the PRF Grid" }[activeSection]) && (
        <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14, marginTop:-8 }}>
          <button onClick={() => onOpenMarket(activeSection)}
            style={{ display:"inline-flex", alignItems:"center", gap:7, cursor:"pointer", fontFamily:"inherit",
              border:"1px solid #cdd9cd", borderRadius:9, padding:"8px 15px", background:"#f6faf6",
              color:"#2d6a2d", fontSize:13, fontWeight:700 }}>
            📈 Open {{ drp: "DRP quoting & market", lrp: "LRP quoting & market", prf: "the PRF Grid" }[activeSection]} →
          </button>
        </div>
      )}
      {activeSection === "fall"   && <FallView   farmer={farmer} prices={prices} priceMapForYear={priceMapForYear} isMobile={isMobile} actions={fieldActions} customerView={customerView} />}
      {activeSection === "spring" && <SpringView farmer={farmer} prices={prices} onChange={onUpdate} isMobile={isMobile} actions={fieldActions} customerView={customerView} />}
      {activeSection === "drp"    && <DRPView farmer={farmer} isMobile={isMobile} onReset={onReset} onUsdaPriceUpdate={(month, classIII, classIV) => {
          // Patch the live USDA_MONTHLY_PRICES object so indemnity estimates update immediately
          if (month && classIII != null) USDA_MONTHLY_PRICES.III[month] = classIII;
          if (month && classIV  != null) USDA_MONTHLY_PRICES.IV[month]  = classIV;
        }} />}
      {activeSection === "lrp"    && <LRPView farmer={farmer} isMobile={isMobile} />}
      {activeSection === "prf" && (
        <>
          <div style={{ display:"flex", gap:6, marginBottom:14, borderBottom:"1px solid #e4ece4" }}>
            {[{ k:"rainfall", label:"🌧️ Rainfall & triggers" },
              { k:"coverage", label:"📑 Coverage & quote" }].map(t => (
              <button key={t.k} onClick={() => setPrfTab(t.k)}
                style={{ padding: isMobile ? "9px 12px" : "10px 18px", fontSize: isMobile ? 12.5 : 13.5,
                  fontWeight: prfTab === t.k ? 800 : 600, color: prfTab === t.k ? "#1a2e1a" : "#60735f",
                  background: prfTab === t.k ? "#fff" : "transparent", border: "none", cursor: "pointer",
                  borderBottom: prfTab === t.k ? "3px solid #4a6e4a" : "3px solid transparent",
                  marginBottom: -1, fontFamily: "inherit" }}>{t.label}</button>
            ))}
          </div>
          {prfTab === "rainfall" && <PRFView farmer={farmer} isMobile={isMobile} />}
          {/* EMBEDDED: pinned to THIS operation, never to the standalone page's
              stored farmer id. See the note on PRFReviewView. */}
          {/* ⚠ mode follows the EFFECTIVE identity, not a literal. FarmerView renders in
              BOTH apps, so hardcoding "portal" here would strip the agent affordances
              off this tab for Mike and Kevin; hardcoding "admin" would put them in
              front of a farmer. isCurrentUserAdmin() is false in preview by design —
              that is exactly what makes preview reproduce the customer's view. */}
          {prfTab === "coverage" && <PRFReviewView farmer={farmer} farmers={null} isMobile={isMobile}
            mode={isCurrentUserAdmin() ? "admin" : "portal"} />}
        </>
      )}

    </div>
  );
}

// ── 578 Documents on Farmer Card ──
function Farmer578Documents({ farmer, season, isMobile, customerView = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const farmerId = farmer.id;

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Get all 578s for this farmer — check _allFarmerIds if farmer has multiple DB rows
        const farmerIds = farmer._allFarmerIds || [farmerId];
        const filter = farmerIds.length === 1
          ? `farmer_id=eq.${farmerId}`
          : `farmer_id=in.(${farmerIds.join(",")})`;
        // ⚠ A CUSTOMER READS ONLY THE COLUMNS THIS CARD SHOWS (blueprint B19, Nov 4
        // 2026). A farmer's REST read of farmer_578s returned every column of their own
        // rows (original_filename, extracted_name, forwarded_to_mandy, matched_by …).
        // sql/132 gives customers portal_farmer_578s(): id, farmer_id, season,
        // tax_year, county, is_nationwide, received_at, storage_path, and only for
        // farms granted to the caller; it then stops a plain farmer login reading the
        // table directly. Until sql/132 is applied the function is missing (404) and
        // the table read below serves the card exactly as before, so this ships safely
        // first. Staff (customerView false) keep the table read, unchanged.
        let rows = null;
        if (customerView) {
          const rpc = await authedFetch(`${SUPABASE_URL}/rest/v1/rpc/portal_farmer_578s`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ p_farmer_ids: farmerIds, p_season: season }),
          });
          if (rpc.ok) rows = await rpc.json();
          else if (rpc.status !== 404) throw new Error(`portal_farmer_578s: HTTP ${rpc.status}`);
        }
        if (rows === null) {
          rows = await sbFetch("farmer_578s", {
            filters: [filter, `season=eq.${season}`]
          });
        }
        setDocs((rows || []).sort((a, b) => new Date(b.received_at) - new Date(a.received_at)));
      } catch (e) { console.error("Failed to load 578s:", e); }
      setLoading(false);
    }
    load();
  }, [farmerId, season, customerView]);

  const ownIds = new Set((farmer._allFarmerIds || [farmerId]).map(String));
  const ownPath = (p) => ownIds.has(String(p || "").split("/")[1] || "");
  async function viewPdf(storagePath) {
    try {
      const url = await sbStorageSignedUrl("farmer-documents", storagePath);
      trackPortalAction("open_document", { screen: "fsa578", kind: "578_pdf" });
      window.open(url, "_blank");
    } catch (err) { alert("Failed to get PDF URL: " + err.message); }
  }

  // Determine policy counties from crop coverage

  const policyCounties = useMemo(() => {
    const counties = new Set();
    (farmer.approvedYields || []).forEach(ay => {
      const isFall = FALL_CROPS_SET.has((ay.crop || "").toUpperCase());
      if ((season === "fall" && isFall) || (season === "spring" && !isFall)) {
        if (ay.county) counties.add(ay.county.toUpperCase());
      }
    });
    return [...counties].sort();
  }, [farmer, season]);

  // Check completeness
  const hasNationwide = docs.some(d => d.is_nationwide);
  const receivedCounties = [...new Set(docs.map(d => (d.county || "").toUpperCase()).filter(Boolean))];
  const missingCounties = hasNationwide ? [] : policyCounties.filter(c => !receivedCounties.includes(c));
  const isComplete = hasNationwide || missingCounties.length === 0;

  if (loading) return null;

  // Show section even if no docs — to show "waiting" status when there are policy counties
  if (docs.length === 0 && policyCounties.length === 0) return null;

  return (
    <div style={{ marginTop: 24, background: "#fff", border: `1px solid ${isComplete && docs.length > 0 ? "#dde8d8" : "#e0c8a0"}`, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ background: isComplete && docs.length > 0 ? "#f4f8f0" : "#fdf8f0", padding: "12px 18px", borderBottom: "1px solid #dde8d8", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a" }}>📋 578s on File</div>
          {docs.length > 0 && hasNationwide && (
            <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 4, background: "#1a4a7a", color: "#fff" }}>NATIONWIDE</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
            background: season === "fall" ? "#f4f0e8" : "#eef6ec",
            color: season === "fall" ? "#6b4f00" : "#2d4a2d" }}>
            {season === "fall" ? "🌾 Fall" : "🌱 Spring"}
          </span>
          {policyCounties.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
              background: isComplete && docs.length > 0 ? "#e0f0e4" : docs.length === 0 ? "#fdecea" : "#fdf6e0",
              color: isComplete && docs.length > 0 ? "#1a5a2a" : docs.length === 0 ? "#c0392b" : "#8a6a00" }}>
              {isComplete && docs.length > 0
                ? `✓ All ${policyCounties.length} counties covered`
                : docs.length === 0
                  ? `✗ No 578s received (${policyCounties.length} counties on policy)`
                  : `⚠ ${missingCounties.length} of ${policyCounties.length} counties missing`}
            </span>
          )}
        </div>
      </div>

      {/* County checklist */}
      {policyCounties.length > 0 && !hasNationwide && (
        <div style={{ padding: "10px 18px", borderBottom: "1px solid #edf2e8", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {policyCounties.map(c => {
            const received = receivedCounties.includes(c);
            return (
              <span key={c} style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 5,
                background: received ? "#e0f0e4" : "#fdecea",
                color: received ? "#1a5a2a" : "#c0392b",
                border: `1px solid ${received ? "#1a5a2a22" : "#c0392b22"}` }}>
                {received ? "✓" : "✗"} {titleCaseName(c)}
              </span>
            );
          })}
        </div>
      )}

      {/* Document list */}
      {docs.length > 0 && (
        <div style={{ padding: "8px 12px" }}>
          {docs.map(r => (
            <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 8px", borderBottom: "1px solid #f0f4f0" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a2e1a" }}>
                  {r.county ? `${r.county} County` : "Unknown County"}
                  {r.is_nationwide && <span style={{ fontSize: 10, fontWeight: 700, color: "#1a4a7a", marginLeft: 8 }}>Nationwide</span>}
                </div>
                <div style={{ fontSize: 11, color: "#60735f" }}>
                  Received {new Date(r.received_at).toLocaleDateString()}
                  {!customerView && r.forwarded_to_mandy && (
                    <span style={{ color: "#2d6a1a", fontWeight: 600 }}>
                      {" · ✓ Emailed to Mandy"}
                      {r.forwarded_at && ` ${new Date(r.forwarded_at).toLocaleDateString()} ${new Date(r.forwarded_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`}
                    </span>
                  )}
                </div>
              </div>
              {/* A customer can only open a PDF filed under their own farm id (the
                  storage policy reads it from the path); for the few filed elsewhere
                  the button would only fail, so it says who has the copy instead. */}
              {r.storage_path && customerView && !ownPath(r.storage_path) && (
                <span style={{ fontSize: 11, color: "#60735f" }}>Ask your agent for this copy</span>
              )}
              {r.storage_path && (!customerView || ownPath(r.storage_path)) && (
                <button onClick={() => viewPdf(r.storage_path)}
                  style={{ fontSize: 11, fontWeight: 700, padding: "6px 14px", borderRadius: 6,
                    border: "1px solid #c8d8c0", background: "#f4f8f0", color: "#3a6e32", cursor: "pointer" }}>
                  View PDF
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {docs.length > 0 && !customerView && <Farmer578Audit farmer={farmer} isMobile={isMobile} />}
    </div>
  );
}

// ── 578 vs the insured book, on the farmer card ──────────────────────────────
// The 578 has been sitting on this card and nothing ever compared it to the
// coverage. This runs api/_audit578.js — which re-uses the SAME parse578 the
// Acreage Report Builder runs, so the reading of the document can never drift
// between the two — and prints where the document and the book disagree.
//
// It WRITES NOTHING. Nothing is stored, nothing is stamped; close the card and
// the result is gone. That is deliberate: this answers "what differs today",
// and a cached answer would quietly age past the next acreage revision.
//
// Admin-only, and gated on the REAL session rather than the effective one: a
// grower's 578 next to the agency's book is an agency view, and preview-as runs
// the farmer's own token (see the preview-is-impersonation note in CLAUDE.md).
const AUDIT578_CLASS = {
  H: { label: "Crop keyed differently than the 578", tone: "bad" },
  A: { label: "On the 578, no coverage in the book", tone: "bad" },
  P: { label: "Prevent plant disagrees", tone: "bad" },
  B: { label: "Acreage differs", tone: "warn" },
  N: { label: "Crop name differs — not a coverage gap", tone: "note" },
  D: { label: "Insured, not on the 578", tone: "note" },
};
function Farmer578Audit({ farmer, isMobile }) {
  const [state, setState] = useState("idle");   // idle | running | done | error
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  if (!isRealAdminSession()) return null;

  async function run() {
    setState("running"); setErr(null); setRep(null);
    try {
      const r = await adminApiFetch("/api/market?fn=audit-578&action=run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ farmer_id: farmer.id }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      setRep(d); setState("done");
    } catch (e) { setErr(e.message); setState("error"); }
  }

  const findings = (rep && rep.findings) || [];
  const tone = { bad: "#c0392b", warn: "#8a6a00", note: "#5a6a5a" };
  const bg   = { bad: "#fdecea", warn: "#fdf6e0", note: "#f2f6f2" };

  return (
    <div style={{ borderTop: "1px solid #e4ece0", padding: "12px 18px", background: "#fbfdfa" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#1a2e1a" }}>
          🔍 Audit the 578 against this policy
          <span style={{ fontWeight: 600, color: "#60735f", marginLeft: 8, fontSize: 11 }}>reads only — nothing is saved</span>
        </div>
        <button onClick={run} disabled={state === "running"}
          style={{ fontSize: 11, fontWeight: 700, padding: "6px 14px", borderRadius: 6,
            border: "1px solid #c8d8c0", background: state === "running" ? "#eef2ee" : "#1a2e1a",
            color: state === "running" ? "#60735f" : "#fff", cursor: state === "running" ? "default" : "pointer" }}>
          {state === "running" ? "Reading the 578…" : state === "done" ? "Run again" : "Run audit"}
        </button>
      </div>

      {state === "running" && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: "#60735f" }}>
          Reading every 578 on file and comparing it to the coverage — this takes a moment per document.
        </div>
      )}
      {state === "error" && (
        <div style={{ marginTop: 10, fontSize: 11.5, fontWeight: 700, color: "#c0392b" }}>Audit failed — {err}</div>
      )}

      {state === "done" && rep && rep.status !== "ok" && (
        <div style={{ marginTop: 10, fontSize: 11.5, fontWeight: 700, color: "#8a6a00" }}>
          {rep.status}
          {/* A partial read is reported, never smoothed over: a county whose 578
              failed to parse is indistinguishable from acreage nobody insured. */}
          {(rep.failed_documents || []).map((f, i) => (
            <div key={i} style={{ fontWeight: 600, color: "#60735f", marginTop: 3 }}>{f.file} — {f.error}</div>
          ))}
        </div>
      )}

      {state === "done" && rep && rep.status === "ok" && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8 }}>
            CY{rep.crop_year} · {(rep.documents || []).length} document(s), {rep.fsa_rows} 578 rows ·
            {" "}{rep.fields_crop_checked} field(s) crop-checked · {(rep.policies || []).join(", ")}
            {rep.superseded_duplicates > 0 && ` · ${rep.superseded_duplicates} duplicate upload(s) ignored`}
          </div>
          {findings.length === 0 ? (
            <div style={{ fontSize: 12, fontWeight: 700, color: "#1a5a2a" }}>
              ✓ No differences — every crop and every checked field reconciles to the 578.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {["H", "A", "P", "B", "N", "D"].flatMap((cls) =>
                findings.filter((f) => f.cls === cls).map((f, i) => {
                  const meta = AUDIT578_CLASS[cls] || { label: cls, tone: "note" };
                  return (
                    <div key={`${cls}-${i}`} style={{ display: "flex", gap: 9, alignItems: "flex-start",
                      background: bg[meta.tone], border: `1px solid ${meta.tone === "note" ? "#e0e8e0" : "#f0d8c8"}`,
                      borderRadius: 7, padding: "7px 10px" }}>
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: tone[meta.tone], whiteSpace: "nowrap",
                        border: `1px solid ${tone[meta.tone]}33`, borderRadius: 4, padding: "1px 5px", marginTop: 1 }}>
                        {meta.label}
                      </span>
                      <span style={{ fontSize: 11.5, color: "#2d3a2d", fontWeight: 600 }}>{f.msg}</span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// POLICY SECTION LIST
// ─────────────────────────────────────────────────────────────────────────────
const SECTION_META = {
  fall:   { label:"Fall Crops",   icon:"🌾", color:"#6b4f00", bg:"#f4f0e8", border:"#c8a840", crops:["WHEAT","BARLEY","TRITICALE","RYE","WINTER WHEAT"] },
  spring: { label:"Spring Crops", icon:"🌱", color:"#2d4a2d", bg:"#eef6ec", border:"#4a7a2a", crops:["CORN","SOYBEANS","DRY BEANS"] },
  drp:    { label:"DRP",          icon:"🥛", color:"#1a4a7a", bg:"#e8f0fa", border:"#4a70aa", crops:[] },
  lrp:    { label:"LRP",          icon:"🐄", color:"#7a3b10", bg:"#fdf1e8", border:"#c86030", crops:[] },
  prf:    { label:"PRF",          icon:"🌧️",  color:"#3a3a7a", bg:"#f0f0fa", border:"#6a6aaa", crops:[] },
};

// ─────────────────────────────────────────────────────────────────────────────
// BOOK — one row per GROWER, every line of business on the same page.
//
// Replaces the five separate coverage lists (Spring / Fall / DRP / LRP / PRF).
// Those screens matched how the data is STORED — `farmers` is really a list of
// policy CARDS keyed on name + policy number — so a grower carrying corn, wheat
// and a DRP endorsement appeared as three cards on three different screens, and
// you had to know which one to open. Here the person is the row and coverage is
// a facet.
//
// The merge + dedup below is the grouping logic from the old Renewal Worklist,
// which had already solved this: union the coverage arrays across a grower's
// cards, then dedup on identity WITHOUT crop year so a farm carrying both a 2026
// card and an early 2027 renewal isn't double-counted.
//
// `lockedLob` lets the portal force the facet to the LOBs a user is granted, so
// the same component is safe to reuse there (hide-then-opt-in, never opt-out).
// ─────────────────────────────────────────────────────────────────────────────
// Order matters — it's the order chips appear on every row, so a grower's coverage
// mix is comparable at a glance down the column. Colour comes from SECTION_META so
// a Spring chip here is the same green as the Spring screen, not a second opinion.
// No emoji: at chip size they rendered as mush and read as decoration rather than
// data (and 🌧️/🐄 in particular were unrecognisable in the first build).
const BOOK_LOBS = [
  { key: "spring", label: "Spring" },
  { key: "fall",   label: "Fall" },
  { key: "drp",    label: "DRP" },
  { key: "lrp",    label: "LRP" },
  { key: "prf",    label: "PRF" },
];
const titleCaseCounty = (s) =>
  String(s || "").toLowerCase().split(/\s+/).filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

// Clover Ag's own writing agents. `agent_name` in the book is messy — one agent
// is split across many spellings ("MICHAEL J MEISENZAHL" / "MEISENZAHL" / "Mike
// Meisenzahl" / the "5727-03" R&H code) and transferred/other-AIP policies carry
// the PRIOR agency's agent. canonAgent() collapses the three real agents to a
// clean label and returns null for everyone else, so the Agent facet lists only
// the people who actually write for the agency (a farmer's own card still shows
// its stored agent verbatim).
// Order-insensitive name key for merging the same customer across name formats.
// M13 stores "RICE, CRAIG" while a manual upload stored "CRAIG RICE" — same person,
// split into two records + a stranded fall-crop policy. Sorting the tokens makes
// "Last, First" and "First Last" collapse to one key so the unified card reunites
// them. (A plain alnum-strip is order-sensitive and kept them apart.)
const sortNameKey = (s) => (s || "").trim().toLowerCase()
  .replace(/[^a-z0-9]+/g, " ").trim().split(/\s+/).filter(Boolean).sort().join(" ");

// "MM KK Service Account" is a shared Mike/Kevin producer account — a legit AOR
// set manually (never appears in the P55 roster). Options offered in the AOR editor.
const MM_KK_SERVICE = "MM KK Service Account";
// Hylant is the co-brokered book (NAU agency 95-3636, Spartan/Hylant). Like the
// MM/KK service account it is a real Agent of Record that never appears in a P55
// roster, so it only ever arrives as a manual override — hence an option in the
// AOR editor and a canonical name here rather than something the sync can write.
const HYLANT = "Hylant";
const AGENCY_AGENTS = ["Mike Meisenzahl", "Kevin Keenan", "Michael Mock", "Daniele Harris", MM_KK_SERVICE, HYLANT];
const canonAgent = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return null;
  if (/service account|mm\s*kk|kk\s*mm/i.test(s)) return MM_KK_SERVICE;   // shared MM/KK account (manual override)
  // Before the individual-name tests, so "Hylant / Meisenzahl" reads as Hylant.
  if (/hylant/i.test(s)) return HYLANT;
  if (/meisenz/i.test(s) || s === "5727-03") return "Mike Meisenzahl";
  if (/keenan/i.test(s)) return "Kevin Keenan";
  if (/\bmock\b/i.test(s)) return "Michael Mock";
  if (/harris/i.test(s)) return "Daniele Harris";   // R&H writing agent, code 60206 (P55 roster)
  return null; // prior / external agent — not an agency-agent filter option
};

// ─────────────────────────────────────────────────────────────────────────────
// PREMIUM ANALYTICS — CONFIDENTIAL (Kevin + Mike only)
// ─────────────────────────────────────────────────────────────────────────────
// Gross premium by agent, by product, year over year: what grew, what fell.
//
// Gated three ways, because this is the one screen where a leak is a business
// problem rather than a UI bug: `adminOnly` in NAV_MODEL hides it, the screen
// dispatch re-checks isCurrentUserAdmin(), and sql/009 revokes the underlying
// table reads from every non-admin session. The first two are convenience; the
// SQL is the one that actually enforces it.
//
// "Gross premium" = total premium (producer premium + RMA subsidy) — the same
// basis `historical_summaries.gross_premium` uses, so the two years are one
// number and not two conventions. Net/producer premium is shown beside it.
//
// TWO SOURCES, ONE FACT SHAPE. A closed year lives in `historical_summaries`
// (the frozen end-of-year snapshot, one row per coverage). The current year has
// no snapshot yet, so it is assembled live from crop_coverage + prf_policies +
// lrp_endorsements + drp_endorsements. buildPremiumFacts() normalizes both into
// the same record so every rollup below is source-agnostic — and because the
// live side reads the same tables the daily syncs write, the current year
// updates on its own as R&H and NAU report. Nothing to re-run.
//
// A year is treated as CLOSED when historical_summaries has rows for it. That
// is deliberately data-driven: the day the CY2026 snapshot is written, 2026
// stops being labelled "still reporting" without a code change.
// ─────────────────────────────────────────────────────────────────────────────
const PA_PRODUCTS = ["MPCI", "SCO", "ECO", "PRF", "LRP", "DRP", "STAX"];
// Carriers that only ever appear as a PRIOR AGENT's book. A transfer-in is loaded
// under the original agency's policy numbers and keeps their writing agent, so it
// is the customer's history, not premium this agency produced. Excluded from
// Premium Analytics on both the live and snapshot sides. Add an AIP here only when
// every row under it is somebody else's business.
const PA_PRIOR_AGENT_AIPS = new Set(["HUDSON"]);
// Display order for product columns/rows. Base first, then the layers that sit
// on top of it, then the standalone lines.
const PA_PRODUCT_ORDER = ["MPCI", "SCO", "ECO", "STAX", "PRF", "LRP", "DRP", "OTHER", "UNKNOWN"];
// Products whose "crop" is only the product restated — they get no Crop chip,
// because DRP is always MILK, LRP is always cattle and PRF is always pasture.
const PA_PRODUCT_IS_ITS_OWN_CROP = new Set(["DRP", "LRP", "PRF"]);
const PA_PRODUCT_NOTE = {
  MPCI: "Base MPCI — RP, YP and APH plans",
  SCO:  "Supplemental Coverage Option — plan 31 on a yield base, 32 (labelled ARPI) on a revenue base",
  ECO:  "Enhanced Coverage Option",
  PRF:  "Pasture, Rangeland & Forage",
  LRP:  "Livestock Risk Protection",
  DRP:  "Dairy Revenue Protection",
  STAX: "Stacked Income Protection",
};

// Map an RMA plan string to the product it is sold as. `plan` comes from
// crop_coverage.plan on the live side and historical_summaries.type on the
// snapshot side — the same vocabulary, which is why one mapper serves both.
// ARPI is NOT its own product: RMA plan 32 is how SCO-RP is written, and
// counting it separately hides SCO's real size (see the ARPI=SCO note in
// the classifier work). mpci_plan_code is the fallback when plan is blank.
function paProduct(plan, planCode) {
  const p = String(plan || "").toUpperCase().trim();
  if (p) {
    if (p.startsWith("ECO")) return "ECO";
    if (p.startsWith("SCO") || p === "ARPI") return "SCO";
    if (p === "STAX") return "STAX";
    if (p === "PRF" || p === "RI") return "PRF";
    if (p === "LRP") return "LRP";
    if (p === "DRP") return "DRP";
    if (p === "RP" || p === "YP" || p === "APH" || p === "RP-HPE") return "MPCI";
  }
  const c = String(planCode || "").trim();
  // 87 is ECO on a YIELD base (ECO-YP), not SCO — every plan-87 row in the book
  // is coverage 0.95 and sits on an APH/YP base, never RP. SCO's top is 0.86
  // through CY2026 and 0.90 from CY2027 (RMA PM-26-036), so it is below 0.95 in
  // BOTH regimes and the discriminator holds — but do not restate it as "SCO
  // caps at 0.86", which is a crop-year answer, not a constant.
  if (c === "88" || c === "87" || c === "89") return "ECO";
  // 31 = SCO on a yield base, 32 = SCO on a revenue base (the "ARPI" label).
  // 31 is NOT STAX — STAX is cotton-only and this agency writes no cotton.
  if (c === "32" || c === "31") return "SCO";
  if (c === "01" || c === "02" || c === "90") return "MPCI";
  return p ? "OTHER" : "UNKNOWN";
}

const paNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

// One label per crop across BOTH sources. The CY2025 snapshot files PRF under
// two different crop names — "PASTURE,RANGELAND,FORAGE" (24 rows) and "PRF"
// (24 rows) — while the live prf_policies side only ever produces the first.
// Un-normalized, the Crop facet showed both as separate chips and picking "PRF"
// matched 2025 rows and NOTHING in 2026, so the page reported $0 PRF premium
// against a real book. Same shape for PROC BEANS vs PROCESSING BEANS.
// Cattle is deliberately NOT collapsed: FED and FEEDER are different LRP
// commodities and merging them would hide a real distinction.
const PA_CROP_ALIASES = {
  "PRF": "PASTURE, RANGELAND, FORAGE",
  "RI": "PASTURE, RANGELAND, FORAGE",
  "PASTURE,RANGELAND,FORAGE": "PASTURE, RANGELAND, FORAGE",
  "PROC BEANS": "PROCESSING BEANS",
};
function paCrop(raw) {
  const s = String(raw || "").toUpperCase().replace(/\s+/g, " ").trim();
  if (!s) return "—";
  return PA_CROP_ALIASES[s] || s;
}

// A policy number with the crop year stripped, so the same policy can be followed
// across years. NAU embeds it (NY-944-1025814-26 → NY-944-1025814); R&H's MP-
// numbers don't carry one and pass through untouched.
function paPolicyStem(policyNumber) {
  return String(policyNumber || "").toUpperCase().trim().replace(/-\d{2}$/, "");
}

// WHO WROTE THIS POLICY — one rule, and it is a FACTORY because the answer
// depends on the whole policy set, not on one row.
//
// A manual `agent_name_override` is a deliberate statement about who owns that
// policy, so it holds for the policy's whole history — otherwise moving a book to
// the shared MM KK account reads as Mike losing $1.3M and MM KK inventing it from
// nothing, when no business actually moved. Keyed on the policy STEM, never on the
// farm: the override is set per policy and 14 of the 16 affected farms are mixed
// (Sullivan Farms: 3 of 4 policies overridden), so restating whole farms would hand
// MM KK premium that is still Mike's. The stored override on a row always wins over
// an inherited one; where one stem carries different overrides in different years,
// the most recent crop year is the current AOR.
//
// ⚠ THIS WAS A CLOSURE INSIDE `buildPremiumFacts` UNTIL 2026-08-27, so the
// Outstanding Premium pane could not ask who wrote a policy without growing a
// SECOND copy of it. This repo's own record is what happens then — three news
// handlers drifted twice, two guarantee engines disagreed about one farm's
// forward-sell ceiling. It is a factory both callers share instead.
function paAgentResolver(policies) {
  const overrideByStem = new Map();
  [...(policies || [])]
    .filter(p => p && p.agent_name_override && paPolicyStem(p.policy_number))
    .sort((a, b) => paNum(a.crop_year) - paNum(b.crop_year))
    .forEach(p => overrideByStem.set(paPolicyStem(p.policy_number), p.agent_name_override));
  return (policyNumber, ownOverride, storedAgent) => {
    if (ownOverride) return canonAgent(ownOverride);
    const stem = paPolicyStem(policyNumber);
    return canonAgent((stem && overrideByStem.get(stem)) || storedAgent);
  };
}

// Normalize both sources into one fact row. Returns [] rather than throwing on
// a missing table so one RLS denial can't blank the whole screen.
function buildPremiumFacts({ policies = [], coverage = [], prfRows = [], lrpRows = [], drpRows = [], hist = [], farmerRows = [] }) {
  const farmerById = new Map();
  farmerRows.forEach(f => { if (f && f.id) farmerById.set(f.id, f); });
  const demoIds = new Set(farmerRows.filter(isDemoFarmer).map(f => f.id));
  const polById = new Map();
  policies.forEach(p => { if (p && p.id) polById.set(p.id, p); });

  const facts = [];
  // Demo accounts (SPARTAN INSURANCE FARMS / DEMO-* policy numbers) are cloned
  // from a real customer for prospect meetings. Left in, the clone's $1.19M of
  // DRP lands in the book totals twice.
  const isDemoPolicy = (pol) =>
    !pol || demoIds.has(pol.farmer_id) || /^DEMO/i.test(String(pol.policy_number || ""));

  // Business the customer brought WITH them, written by whoever had them before.
  // It's loaded under the prior agency's own policy numbers and carries their
  // agent (see the transfer-in convention), so it isn't premium this agency
  // wrote and it isn't ours to count — it was landing under "Unattributed"
  // instead. Laier Dairy's $58,675 of Michigan DRP + LRP is the whole of it
  // today. Their CY2027 NAU rewrite is a different policy row and still counts.
  const isPriorAgentPolicy = (pol) => PA_PRIOR_AGENT_AIPS.has(String((pol && pol.aip) || "").toUpperCase());

  // ── AOR restatement ───────────────────────────────────────────────────────
  // A manual agent_name_override is a deliberate statement about who owns that
  // policy, so it should hold for the policy's whole history — otherwise moving
  // a book to the shared MM KK account reads as Mike losing $1.3M and MM KK
  // inventing it from nothing, when no business actually moved.
  //
  // Keyed on the policy STEM, never on the farm. The override is set per policy
  // and 14 of the 16 affected farms are mixed (Sullivan Farms: 3 of 4 policies
  // overridden), so restating whole farms would hand MM KK premium that is still
  // Mike's in the current year too. Stem-matching restates exactly the policies
  // Kevin actually reassigned: 30 of the 40 override stems have CY2025 rows.
  //
  // The stored override on a row always wins over an inherited one; when the
  // same stem carries different overrides in different years, the most recent
  // crop year is the current AOR and takes precedence.
  const agentFor = paAgentResolver(policies);

  // ── Customer identity across years ────────────────────────────────────────
  // The name key (normFarmerName) collapses case, word order, entity suffix and
  // single-letter middle initials, and deliberately nothing else — it refuses to
  // guess. That leaves a class it cannot join, where the AIP simply spelled the
  // insured differently in the two files:
  //   snapshot "MARQUART THOMAS"                    live "THOMAS ADAM MARQUART"
  //   snapshot "…BENEDICT DBA ARBOR FARMS"          live "…BENEDICT (ARBOR FARMS)"
  //   snapshot "JAMES ARCHER"                       live "JAMES ARCHER JR"
  // Each read as new business AND not-yet-reported at once — Marquart alone
  // showed $593,636 of "new" against $402,308 "lost", one man counted twice.
  //
  // The AIP's own POLICY NUMBER settles all three without guessing at names: the
  // stem is stable across crop years (NAU appends the year, R&H never carried
  // one), so 4005428 is the same policy in the 2025 snapshot and the live 2026
  // book. Verified per case — Benedict keeps 1025814 + 6066195, and the 2025
  // "JAMES ARCHER" rows carry 6064198 / 9007782, which are exactly the policies
  // live JAMES ARCHER JR holds today (a second, different James Archer holds
  // 6086229, which is why the NAME can never decide this one).
  //
  // A stem claimed by two different customers is REFUSED, not guessed — the same
  // discipline resolveExistingFarmerName follows. That is also what protects a
  // policy that genuinely changed hands: both years are live rows, the stem is
  // contested, and the name key stays in charge.
  const custKeyByStem = new Map();
  policies.forEach(p => {
    if (!p || isDemoPolicy(p) || isPriorAgentPolicy(p)) return;
    const f = p.farmer_id ? farmerById.get(p.farmer_id) : null;
    const k = normFarmerName(f && f.name);
    const stem = paPolicyStem(p.policy_number);
    if (!k || !stem) return;
    if (!custKeyByStem.has(stem)) custKeyByStem.set(stem, k);
    else if (custKeyByStem.get(stem) !== k) custKeyByStem.set(stem, null);
  });
  const custKeyFor = (name, policyNumber, rowId) => {
    const stem = paPolicyStem(policyNumber);
    return (stem && custKeyByStem.get(stem))
        || normFarmerName(name)
        || ("pol:" + String(policyNumber || rowId));
  };

  const liveMeta = (pol) => {
    const f = pol.farmer_id ? farmerById.get(pol.farmer_id) : null;
    const name = (f && f.name) || null;
    return {
      src: "live",
      agent: agentFor(pol.policy_number, pol.agent_name_override, pol.agent_name),
      agentRaw: pol.agent_name_override || pol.agent_name || null,
      aip: pol.aip || null,
      state: pol.state || null,
      customer: name || pol.policy_number || "(unknown)",
      // normFarmerName is the book's canonical customer key — it collapses case,
      // punctuation, word order, entity suffix and single-letter middle initials,
      // which is exactly the variation between the snapshot's "EDMUND NATHANAEL"
      // and the live "NATHANIEL G EDMUND". Measured against the real book it
      // matches 204 of 246 CY2026 customers back to CY2025 where the plainer
      // sortNameKey managed 181 — 23 customers that would otherwise have read as
      // new business AND lost business in the same view. The policy stem covers
      // what the name cannot; see custKeyFor above.
      custKey: custKeyFor(name, pol.policy_number, pol.id),
      policyNumber: pol.policy_number || null,
    };
  };

  // Does this row's gross actually decompose into subsidy + producer premium?
  // Rows carrying a gross with no split are real premium but contribute 0 to the
  // producer-premium view, silently understating it. Flagged rather than guessed
  // at — the split lives on the AIP statement, not anywhere we can derive it.
  const splitOk = (gross, subsidy, net) =>
    !(gross > 0) || Math.abs(gross - (paNum(subsidy) + paNum(net))) <= 1;

  const pushLive = (pol, row) => {
    if (!pol || isDemoPolicy(pol) || isPriorAgentPolicy(pol)) return;
    facts.push({ ...liveMeta(pol), ...row, netOk: splitOk(row.gross, row.subsidy, row.net) });
  };

  coverage.forEach(r => {
    const pol = polById.get(r.policy_id);
    if (!pol) return;
    pushLive(pol, {
      year: pol.crop_year, product: paProduct(r.plan, r.mpci_plan_code),
      crop: paCrop(r.crop), county: r.county || null,
      acres: paNum(r.acres), gross: paNum(r.total_premium),
      subsidy: paNum(r.subsidy), net: paNum(r.net_premium), liability: paNum(r.liability),
    });
  });

  prfRows.forEach(r => {
    const pol = polById.get(r.policy_id);
    if (!pol) return;
    pushLive(pol, {
      year: pol.crop_year, product: "PRF",
      crop: paCrop("PRF"), county: r.county || null,
      acres: paNum(r.total_acres), gross: paNum(r.total_premium),
      subsidy: paNum(r.subsidy), net: paNum(r.net_premium), liability: paNum(r.total_liability),
    });
  });

  // Livestock rolls to the next reinsurance year on July 1, so the crop year of
  // a DRP/LRP endorsement comes from its effective date, never the policy row —
  // a DRP endorsement sold in July 2025 is CY2026 business.
  // Crop year for the livestock lines, taken from the AIP — never derived.
  // They do NOT share a rule, which is exactly why each takes its own source:
  //   DRP — `soi_crop_year`, written by the sync from P18 field 1, i.e. the year
  //         NAU filed the endorsement under.
  //   LRP — the policy row. The AIP has already applied the reinsurance roll when
  //         it assigned the endorsement to a policy year; deriving again
  //         double-applies it. On this basis we match Rain & Hail's CY2026 LRP
  //         Policy Detail exactly: 9 policies / 132 endorsements / $274,832.
  // The date derivation survives only as a last resort for 74 legacy CY2025 DRP
  // rows loaded by hand before soi_crop_year existed.
  const drpYear = (r, pol) => r.soi_crop_year ?? (pol && pol.crop_year) ?? deriveDrpCropYear(r.effective_date) ?? null;
  const lrpYear = (r, pol) => (pol && pol.crop_year) ?? null;

  lrpRows.forEach(r => {
    const pol = polById.get(r.policy_id);
    if (!pol) return;
    pushLive(pol, {
      // The endorsement names its own commodity ("Fed Cattle" / "Feeder Cattle"),
      // which is what the snapshot side already stores and what RMA rates
      // separately. Hardcoding "CATTLE" here put a third, meaningless bucket on
      // the crop chart beside the two real ones. Unborn Calf endorsements are
      // Feeder Cattle and belong in that bucket, not their own.
      year: lrpYear(r, pol), product: "LRP", crop: paCrop(r.commodity || "CATTLE"), county: pol.county || null,
      acres: 0, gross: paNum(r.total_premium), subsidy: paNum(r.subsidy),
      net: paNum(r.producer_premium), liability: paNum(r.liability),
    });
  });

  drpRows.forEach(r => {
    const pol = polById.get(r.policy_id);
    if (!pol) return;
    pushLive(pol, {
      year: drpYear(r, pol), product: "DRP", crop: paCrop("MILK"), county: pol.county || null,
      acres: 0, gross: paNum(r.total_premium), subsidy: paNum(r.subsidy),
      net: paNum(r.producer_premium), liability: paNum(r.liability),
    });
  });

  hist.forEach(r => {
    if (PA_PRIOR_AGENT_AIPS.has(String(r.aip || "").toUpperCase())) return;
    facts.push({
      src: "hist", year: r.crop_year, product: paProduct(r.type, null),
      // The snapshot is frozen and still names the agent who wrote the policy
      // that year; agentFor restates it onto the current AOR where one was set
      // manually, so the year-over-year agent comparison is like for like.
      agent: agentFor(r.policy_number, null, r.agent), agentRaw: r.agent || null,
      aip: r.aip || null, state: r.state || null,
      customer: r.farmer_name || r.policy_number || "(unknown)",
      custKey: custKeyFor(r.farmer_name, r.policy_number, r.id),
      policyNumber: r.policy_number || null,
      crop: paCrop(r.crop), county: r.county || null,
      acres: paNum(r.acres), gross: paNum(r.gross_premium),
      subsidy: paNum(r.subsidy), net: paNum(r.net_premium),
      netOk: splitOk(paNum(r.gross_premium), r.subsidy, r.net_premium),
      // The snapshot never carried liability. 0 would read as "no exposure";
      // null keeps it out of the average and renders as "—".
      liability: null,
    });
  });

  // One display name per customer. custKey already collapses "EDMUND NATHANAEL"
  // and "NATHANIEL G EDMUND" to one identity, but the LABEL still differed by
  // source — so grouping a chart by customer split 253 farms into 587 buckets,
  // each appearing to exist in only one year. Prefer the live spelling, since
  // that's the farmers table the rest of the app displays.
  const labelByKey = new Map();
  facts.forEach(f => {
    if (!f.custKey) return;
    const cur = labelByKey.get(f.custKey);
    if (!cur || (cur.src !== "live" && f.src === "live")) labelByKey.set(f.custKey, { name: f.customer, src: f.src });
  });
  facts.forEach(f => {
    const l = labelByKey.get(f.custKey);
    if (l && l.name) f.customer = l.name;
  });

  return facts;
}

const PA_UNATTRIBUTED = "Unattributed";
const paSum = (rows, field) => rows.reduce((s, r) => s + paNum(r[field]), 0);
// null when there's no prior-year base — an infinite % on brand-new business is
// noise, and "new" is a more useful label than "+∞%".
const paPct = (now, before) => (before > 0 ? (now - before) / before : null);
const paSignColor = (d) => (d > 0 ? "#2d6a2d" : d < 0 ? "#a33" : "#60735f");
const paDeltaStr = (d) => (d > 0 ? "+" : "") + fmtD0(Math.round(d)).replace("$-", "-$");
const paPctStr = (p) => (p == null ? "—" : (p > 0 ? "+" : "") + (p * 100).toFixed(1) + "%");

// Two-bar comparison. Widths are shares of the largest value in the section so
// the eye compares rows against each other, not against a per-row max.
function PABar({ before, now, max }) {
  const w = (v) => (max > 0 ? Math.max(v > 0 ? 1.5 : 0, (v / max) * 100) : 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 90 }}>
      <div style={{ height: 7, borderRadius: 3, background: "#c9d8c9", width: w(before) + "%" }} />
      <div style={{ height: 7, borderRadius: 3, background: "#2d6a2d", width: w(now) + "%" }} />
    </div>
  );
}

// Prior year vs current year is a before→after pair on one measure, so it takes
// ONE hue in two shades (an ordinal ramp), not two categorical colours — the
// reader should see "same thing, two points in time", not "two subjects".
// Validated as an ordinal ramp against the white card surface: monotone
// lightness, ΔL well clear of the 0.06 floor, light end 2.5:1 on surface,
// hue spread 1°. Don't swap either value without re-validating.
const PA_PRIOR = "#7ab07a";
const PA_NOW   = "#2d6a2d";

// Dimensions the chart can group by. Every getter has to work on BOTH sources —
// the frozen snapshot and the live tables — or a year silently collapses into
// one bucket. `state` is deliberately absent: the snapshot never carried it, so
// grouping by it would show all of CY2025 as "—".
const PA_DIMS = [
  { key: "product",  label: "Product",  get: (f) => f.product },
  { key: "agent",    label: "Agent",    get: (f) => f.agent || PA_UNATTRIBUTED },
  { key: "crop",     label: "Crop",     get: (f) => f.crop || "—" },
  // County is qualified by state because county NAMES repeat across state lines
  // and an unqualified chip silently blends them. Steuben is the live case: it
  // exists in both Indiana and New York, so a bare "STEUBEN" filter pulled
  // Bridgewater Farming's Indiana corn in alongside genuine NY Steuben growers.
  // Somerset/Cambria/Blair/Huntingdon looked like the same problem but were only
  // bad state data, since those are PA-only counties. Every fact carries a state
  // (live from policies.state, snapshot from historical_summaries.state), so the
  // qualifier is never blank.
  // Only qualify a county we actually have. Appending the state unconditionally
  // split "no county" into one chip per state ("— · NY", "— · PA", …), which is
  // noise: a row with no county has nothing to disambiguate.
  { key: "county",   label: "County",   get: (f) => f.county
      ? String(f.county).toUpperCase() + (f.state ? " · " + String(f.state).toUpperCase() : "")
      : "—" },
  { key: "state",    label: "State",    get: (f) => String(f.state || "—").toUpperCase() },
  { key: "aip",      label: "Carrier",  get: (f) => f.aip || "—" },
  { key: "customer", label: "Customer", get: (f) => f.customer || "—" },
];

// Short money for axis ticks — "$8.6M" reads at tick size where "$8,588,878"
// doesn't, and the exact figure is always a hover away.
function paCompact(n) {
  const v = Math.abs(n);
  if (v >= 1e6) return "$" + (n / 1e6).toFixed(v >= 1e7 ? 0 : 1) + "M";
  if (v >= 1e3) return "$" + Math.round(n / 1e3) + "k";
  return "$" + Math.round(n);
}

// Vertical grouped columns — the two years sit SIDE BY SIDE within each
// category, so Mike's 2025 and 2026 read as a pair and can be compared against
// Kevin's pair beside it.
//
// Columns are capped at 24px and the plot keeps a minimum width per group, so a
// long category list scrolls inside its own container rather than crushing every
// column to a sliver. Category labels stay horizontal (never rotated — rotated
// axis labels are the thing horizontal bars exist to avoid); when the names get
// long or numerous, the Horizontal toggle is the better read.
function PAColumnChart({ rows, yearA, yearB, isMobile }) {
  const max = rows.reduce((m, r) => Math.max(m, r.a, r.b), 0);
  if (!rows.length || !max) {
    return <div style={{ fontSize: 12.5, color: "#677167", padding: "18px 2px" }}>Nothing to chart under these filters.</div>;
  }
  const H = isMobile ? 180 : 250;
  const groupW = isMobile ? 62 : 78;
  const colW = isMobile ? 18 : 24;
  const ticks = [1, 0.75, 0.5, 0.25, 0];
  const h = (v) => (v > 0 ? Math.max(2, (v / max) * H) : 0);
  // 4px rounded data-end at the top, square where it meets the baseline.
  const col = (v, color) => ({ width: colW, height: h(v), background: color, borderRadius: "4px 4px 0 0" });
  const swatch = (c) => ({ width: 10, height: 10, borderRadius: 2, background: c, flex: "0 0 auto" });

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 10, fontSize: 11.5, color: "#4a6e4a" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={swatch(PA_PRIOR)} />{yearA}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={swatch(PA_NOW)} />{yearB}</span>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {/* y axis gutter */}
        <div style={{ width: isMobile ? 40 : 52, flex: "0 0 auto", height: H, position: "relative" }}>
          {ticks.map(t => (
            <div key={t} style={{ position: "absolute", top: (1 - t) * H - 6, right: 0, fontSize: 10, color: "#677167" }}>
              {paCompact(max * t)}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 4 }}>
          <div style={{ position: "relative", minWidth: rows.length * groupW }}>
            {/* recessive gridlines, behind the marks */}
            {ticks.map(t => (
              <div key={t} style={{ position: "absolute", left: 0, right: 0, top: (1 - t) * H,
                                    borderTop: t === 0 ? "1px solid #c9d8c9" : "1px solid #eef3ee" }} />
            ))}
            <div style={{ display: "flex", alignItems: "flex-end", height: H, position: "relative" }}>
              {rows.map(r => (
                <div key={r.key} style={{ width: groupW, flex: "0 0 auto", display: "flex", justifyContent: "center", alignItems: "flex-end" }}>
                  {/* 2px surface gap between the pair — a gap, never a border */}
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                    <div style={col(r.a, PA_PRIOR)} title={`${r.key} · ${yearA}: ${fmtD0(Math.round(r.a))}`} />
                    <div style={col(r.b, PA_NOW)} title={`${r.key} · ${yearB}: ${fmtD0(Math.round(r.b))} (${paDeltaStr(r.d)}, ${paPctStr(r.pct)})`} />
                  </div>
                </div>
              ))}
            </div>
            {/* category labels + the current-year figure, direct-labelled once per group */}
            <div style={{ display: "flex" }}>
              {rows.map(r => (
                <div key={r.key} style={{ width: groupW, flex: "0 0 auto", paddingTop: 6, textAlign: "center", overflow: "hidden" }} title={r.key}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: "#1a2e1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {paCompact(r.b)}
                  </div>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: paSignColor(r.d), whiteSpace: "nowrap" }}>{paPctStr(r.pct)}</div>
                  <div style={{ fontSize: 10.5, color: "#4a6e4a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 }}>
                    {r.key}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Horizontal grouped bars. Better than columns for long-named categories
// (customers, counties) and for a long Top-N, where column labels run out of room.
function PAChart({ rows, yearA, yearB, isMobile }) {
  const max = rows.reduce((m, r) => Math.max(m, r.a, r.b), 0);
  if (!rows.length || !max) {
    return <div style={{ fontSize: 12.5, color: "#677167", padding: "18px 2px" }}>Nothing to chart under these filters.</div>;
  }
  const labelW = isMobile ? 92 : 150;
  const pct = (v) => (v > 0 ? Math.max(0.6, (v / max) * 100) : 0);
  // 4px rounded data-end, square at the baseline — the bar grows from the axis.
  const bar = (v, color) => ({
    width: pct(v) + "%", height: 11, background: color,
    borderRadius: "0 4px 4px 0", minWidth: v > 0 ? 2 : 0, transition: "width .18s ease",
  });
  const swatch = (c) => ({ width: 10, height: 10, borderRadius: 2, background: c, flex: "0 0 auto" });

  return (
    <div>
      {/* ≥2 series, so a legend is always present — identity is never colour alone. */}
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12, fontSize: 11.5, color: "#4a6e4a" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={swatch(PA_PRIOR)} />{yearA}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={swatch(PA_NOW)} />{yearB}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rows.map(r => (
          <div key={r.key} style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ width: labelW, flex: "0 0 auto", fontSize: 12, fontWeight: 600, color: "#1a2e1a",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.key}>
              {r.key}
            </div>
            {/* 2px surface gap between the touching bars — a gap, never a border. */}
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={bar(r.a, PA_PRIOR)} title={`${r.key} · ${yearA}: ${fmtD0(Math.round(r.a))}`} />
              <div style={bar(r.b, PA_NOW)}   title={`${r.key} · ${yearB}: ${fmtD0(Math.round(r.b))} (${paDeltaStr(r.d)}, ${paPctStr(r.pct)})`} />
            </div>
            {/* Direct-labelled on the current year only — a number on every mark is noise. */}
            <div style={{ width: isMobile ? 92 : 132, flex: "0 0 auto", textAlign: "right", lineHeight: 1.25 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2e1a" }}>{fmtD0(Math.round(r.b))}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: paSignColor(r.d) }}>{paDeltaStr(r.d)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// WHO STILL OWES PREMIUM — the book assembly, bridging the stored rows to the
// rule. The crop half runs through buildBillingRule, which is the SAME rule the
// farmer card's billing panel renders, so this list and that card cannot
// disagree about what one farm owes.
function buildOutstandingBook({ policies, coverage, prfRows, lrpRows, drpRows, farmerRows, payRows, credRows }) {
  const claims = (credRows || []).map(c => ({
    policyId: c.policy_id, claimNumber: c.claim_number, lossCode: c.loss_code,
    policyType: c.policy_type, cropYear: c.crop_year, crop: c.crop,
    county: c.county, claimStages: c.claim_stages, indemnityAmount: c.indemnity_amount,
  }));
  const payments = (payRows || []).map(p => ({
    policyId: p.policy_id, amount: p.amount, paymentType: p.cause_code,
    // WHICH crop year this paid for (sql/054) — the only thing that can show a
    // payment belongs to the policy row it is attached to.
    paymentDate: p.loss_event_date, fileCropYear: p.file_crop_year, flag: p.flag,
  }));
  const cropYearById = new Map((policies || []).map(p => [String(p.id), p.crop_year]));

  // The crop bill, per policy, from the shared rule.
  const covByPolicy = new Map();
  (coverage || []).forEach(r => {
    if (r?.policy_id == null) return;
    const k = String(r.policy_id);
    if (!covByPolicy.has(k)) covByPolicy.set(k, []);
    covByPolicy.get(k).push(r);
  });
  const billed = nonCropBilledByPolicy({ prfRows, lrpRows, drpRows });
  covByPolicy.forEach((rows, pid) => {
    const farmerPremium = rows.reduce((sum, r) => {
      if (r.net_premium != null) return sum + Number(r.net_premium);
      if (r.total_premium != null && r.subsidy != null) return sum + Number(r.total_premium) - Number(r.subsidy);
      return sum + (r.total_premium ? Number(r.total_premium) : 0);
    }, 0);
    const b = buildBillingRule({ rows, claims, policyId: pid,
      cropYear: cropYearById.get(pid), farmerPremium });
    billed.set(pid, b.balanceDue);
  });

  const nameById = new Map((farmerRows || []).map(f => [String(f.id), f.name]));
  // A policy a claim credit settled to $0 was still billed — it reads "settled",
  // not "never billed", so it must not fall off the page.
  const alsoList = [...new Set(claims.filter(c => c.policyId != null).map(c => String(c.policyId)))];
  // The SAME rule Premium Analytics resolves agents with — the factory is passed
  // in, never reimplemented here.
  const rows = buildBookOutstanding({ policies, billedByPolicy: billed, payments,
    farmerNameById: nameById, alsoList, agentFor: paAgentResolver(policies) });
  // `rows` (per POLICY) is what the agent lens re-rolls: narrowing to an agent
  // has to rebuild each farmer from that agent's policies, not filter a list of
  // farm totals somebody else's policies are inside.
  return { rows, byFarmer: rollUpByFarmer(rows) };
}

// ── Outstanding premium — who still owes, and what the figure may claim ──────
//
// ⚠ IT SAYS OUTSTANDING, NEVER "PAST DUE". `crop_coverage.billing_date` is NULL
// on every row of every year and PRF's on all 42, so we hold no due date at all.
// The AIP's statement prints one and it does not reach us. A grower who simply
// is not due yet must not appear on a page that reads as a collections queue.
// Sort columns. `dir` is each column's DEFAULT direction — money and dates want
// the biggest and the most recent first, a name wants A-Z — so one click gives
// the reading somebody actually wants rather than the reverse of it.
const OP_SORTS = [
  { key: "owed",   label: "Amount due",   get: (f) => f.owed,       dir: "desc" },
  { key: "billed", label: "Billed",       get: (f) => f.billed,     dir: "desc" },
  { key: "paid",   label: "Paid",         get: (f) => f.paid,       dir: "desc" },
  { key: "name",   label: "Customer",     get: (f) => f.farmerName, dir: "asc"  },
  { key: "last",   label: "Last payment", get: (f) => f.lastPay,    dir: "desc" },
];

function OutstandingPremiumPane({ book }) {
  const [open, setOpen] = useState(() => new Set());
  const [onlyOwing, setOnlyOwing] = useState(true);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("owed");
  const [sortDir, setSortDir] = useState(null);   // null = the column's own default
  const [agent, setAgent] = useState(null);       // null = every agent
  const [year, setYear]   = useState(null);       // null = every crop year
  // Local copies: this pane is module scope and the parent's `card`/`chipBtn`
  // are declared inside it. Same palette as every other card on the page.
  const card = { background: "#fff", border: "1px solid #e4ece4", borderRadius: 10, padding: 14 };
  const btn = (on) => ({ fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer",
    padding: "5px 11px", borderRadius: 999, border: "1px solid " + (on ? "#2d5a10" : "#dde7dd"),
    background: on ? "#eef6ec" : "#fff", color: on ? "#2d5a10" : "#60735f" });
  if (!book) return <div style={{ ...card, fontSize: 13, color: "#60735f" }}>Loading…</div>;

  // ── The agent lens ────────────────────────────────────────────────────────
  // ⚠ NARROWING TO AN AGENT REBUILDS EVERY FARMER FROM THAT AGENT'S POLICIES.
  // It is deliberately not a filter over farm totals: measured 2026-08-27, 19 of
  // 280 customers carry policies written by more than one agent, so filtering the
  // LIST while showing each farm's whole balance would credit one agent with what
  // another wrote. Sparta Farms owes $185,183 across five policies — that number
  // belongs to nobody in particular until the policies are split.
  const agentOptions = [...new Set((book.rows || []).map(r => agentLabel(r.agent)))]
    .sort((a, b) => (a === UNATTRIBUTED) - (b === UNATTRIBUTED) || a.localeCompare(b));
  const yearOptions = yearsOf(book.rows || []);

  // Agent and year compose, and BOTH narrow the money the same way — the rows
  // are re-rolled from what survives, never filtered as farm totals. The
  // unfiltered case deliberately reuses the book's own rollup so the default
  // view cannot drift from what the rule produced.
  const lens = (rows) => narrowToYear(narrowToAgent(rows, agent), year);
  const all = (agent || year) ? rollUpByFarmer(lens(book.rows || [])) : (book.byFarmer || []);

  // ⚠ A YEAR FILTER HIDES A REAL BALANCE ON ANOTHER YEAR, and that is the one
  // thing it must say. Hansen Farms owes $5,461 on MP-0906029 CY2026 and carries
  // an $8,373 payment stamped against CY2027 of the SAME policy number — a fall
  // crop paid the previous autumn. Filtered to 2026 that credit vanishes and the
  // farm reads as a plain debtor; filtered to 2027 the debt does.
  const elsewhereCount = year == null ? 0
    : new Set(narrowToAgent(book.rows || [], agent)
        .filter(r => Number(r.cropYear) !== Number(year) && r.reconciles && r.outstanding !== 0)
        .map(r => String(r.farmerId ?? r.farmerName))).size;

  // Search reads the customer name AND every policy number on the farm — the
  // statement in your hand carries a policy number, not a name we normalised.
  const needle = q.trim().toLowerCase();
  const hit = (f) => !needle
    || String(f.farmerName || "").toLowerCase().includes(needle)
    || (f.policies || []).concat(f.excludedPolicies || [])
        .some(p => String(p.policyNumber || "").toLowerCase().includes(needle));

  const sortCol = OP_SORTS.find(x => x.key === sortKey) || OP_SORTS[0];
  const dir = sortDir || sortCol.dir;
  const withLast = all.map(f => ({ ...f,
    lastPay: (f.policies || []).reduce((d, p) => (p.lastPaymentDate && (!d || p.lastPaymentDate > d) ? p.lastPaymentDate : d), null) }));
  const shown = withLast
    .filter(f => (onlyOwing ? f.owed > 0 : true) && hit(f))
    .sort((a, b) => {
      const av = sortCol.get(a), bv = sortCol.get(b);
      // A farm that has never paid sorts to the END whichever way the date column
      // points — "no payment" is not "paid a long time ago".
      if (sortCol.key === "last") { if (!av && !bv) return 0; if (!av) return 1; if (!bv) return -1; }
      const c = typeof av === "string" ? String(av).localeCompare(String(bv)) : (Number(av) || 0) - (Number(bv) || 0);
      return dir === "desc" ? -c : c;
    });
  const tot = outstandingTotals(all);
  const excluded = all.flatMap(f => f.excludedPolicies || []);

  const th = { padding: "8px 10px", textAlign: "right", fontSize: 10, fontWeight: 800,
               letterSpacing: "0.08em", color: "#60735f", textTransform: "uppercase",
               borderBottom: "1px solid #e4ece4", whiteSpace: "nowrap" };
  const td = { padding: "9px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12.5 };
  const STATE_LABEL = { unpaid: "Nothing received", partial: "Part paid", settled: "Settled",
                        credit: "Credit balance", not_reconcilable: "Not reconciled" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 22, alignItems: "flex-end" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: "#60735f", textTransform: "uppercase" }}>Outstanding</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{fmtD0(tot.outstanding)}</div>
          <div style={{ fontSize: 11, color: "#60735f" }}>{tot.farmersOwing} of {tot.farmers} customers</div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: "#60735f", textTransform: "uppercase" }}>Billed</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#3a5a3a", fontVariantNumeric: "tabular-nums" }}>{fmtD0(tot.billed)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: "#60735f", textTransform: "uppercase" }}>Paid</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#2d5a10", fontVariantNumeric: "tabular-nums" }}>{fmtD0(tot.paid)}</div>
        </div>
        {tot.credits < 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: "#9a5a1a", textTransform: "uppercase" }}>Credit balances</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#9a5a1a", fontVariantNumeric: "tabular-nums" }}>{fmtD0(tot.credits)}</div>
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => setOnlyOwing(v => !v)} style={btn(onlyOwing)}>
          {onlyOwing ? "Owing only" : "All customers"}
        </button>
      </div>

      {/* ⚠ REFUSED, NOT DROPPED. A payment we can PROVE paid a different crop
          year is not money against this bill, but it is real money the grower
          sent — so it is counted and named rather than silently discarded.
          Measured 2026-09-01: 235 of 334 payments are attached to a policy row
          for a year they did not pay for, because both syncs' backfill paths
          staple a payment to the NEWEST year when no row exists for its own. */}
      {tot.foreignYearCount > 0 && (
        <div style={{ ...card, background: "#fdfaf4", borderColor: "#e8dcc4", fontSize: 12,
                      color: "#7a5a1a", lineHeight: 1.55 }}>
          <b>{tot.foreignYearCount} payment{tot.foreignYearCount === 1 ? "" : "s"} totalling{" "}
          {fmtD0(tot.foreignYearAmount)} are not counted here.</b> Each one came out of an M13 file
          for a crop year this book holds no policy row for, so it was attached to the nearest year
          we do hold — counting it would show a credit against a policy it never paid. The money is
          real; the year it paid for is not on this page. This corrects on the next sync after the
          attachment fix deploys.
        </div>
      )}

      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search customer or policy number…"
          style={{ fontFamily: "inherit", fontSize: 13, padding: "7px 11px", borderRadius: 8,
                   border: "1px solid #dde7dd", minWidth: 260, flex: "0 1 320px", color: "#1a2e1a" }} />
        {q && <button onClick={() => setQ("")} style={{ ...btn(false), padding: "5px 9px" }}>clear</button>}
        <div style={{ width: 1, height: 22, background: "#e4ece4" }} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "#60735f", textTransform: "uppercase" }}>Crop year</span>
        <button onClick={() => setYear(null)} style={btn(year == null)}>All</button>
        {yearOptions.map(y => (
          <button key={y} onClick={() => setYear(year === y ? null : y)} style={btn(year === y)}>{y}</button>
        ))}
        <div style={{ width: 1, height: 22, background: "#e4ece4" }} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "#60735f", textTransform: "uppercase" }}>Agent</span>
        <button onClick={() => setAgent(null)} style={btn(!agent)}>All</button>
        {agentOptions.map(a => (
          <button key={a} onClick={() => setAgent(agent === a ? null : a)} style={btn(agent === a)}>{a}</button>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: "#60735f" }}>
          {shown.length} of {all.length} shown
        </span>
      </div>

      {/* ⚠ The things a filtered total must say out loud, each only when it applies. */}
      {(agent || year) && (
        <div style={{ ...card, background: "#fdfaf4", borderColor: "#e8dcc4", fontSize: 12,
                      color: "#7a5a1a", lineHeight: 1.55, marginTop: -6 }}>
          Showing{" "}
          {year != null && <><b>CY{year}</b> policies</>}
          {year != null && agent && " written by "}
          {agent && <b>{agent === UNATTRIBUTED ? "no resolved agent" : agent}</b>}
          {" "}only — every figure above and below is <b>rebuilt from those policies</b>, not the
          farm's whole balance.
          {agent && <> 19 of 280 customers carry policies written by more than one agent, so a farm
            can appear under two agents with a different number under each.</>}
          {/* The Hansen case: a credit on one crop year and a debt on another are
              different bills, so a year filter can hide either one. */}
          {elsewhereCount > 0 && <> <b>{elsewhereCount} of these customers also have an unsettled
            balance in another crop year</b> — a fall crop is often paid the previous autumn, so a
            debt and a credit on one policy number can sit in different years.</>}
          {" "}Clear the filter for the whole picture.
        </div>
      )}

      {/* The one claim this page is NOT allowed to make, said out loud. */}
      <div style={{ fontSize: 12, color: "#60735f", marginTop: -6, lineHeight: 1.55 }}>
        Payments are the AIP's own M13 <b>P12</b> records — code 00 “Paid by insured”, code 06
        “Paid by insured (livestock only)”, per RMA’s ICE&nbsp;D00060 — joined to the policy the
        AIP stamped them with. <b>We hold no due date</b>: every coverage row’s billing date is
        empty, so this is what is <i>outstanding</i>, not what is late.
        {tot.excludedPolicies > 0 && <> DRP is listed but <b>not counted</b> — see below.</>}
      </div>

      <Card>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f4f8f4" }}>
                {[["name","left"],["billed","right"],["paid","right"],["owed","right"],null,["last","right"]].map((c, i) => {
                  const labels = ["Customer","Billed","Paid","Outstanding","State","Last payment"];
                  if (!c) return <th key={i} style={{ ...th, textAlign: "left" }}>{labels[i]}</th>;
                  const [key, align] = c;
                  const on = sortKey === key;
                  const col = OP_SORTS.find(x => x.key === key);
                  return (
                    <th key={i} style={{ ...th, textAlign: align, cursor: "pointer", userSelect: "none",
                                         color: on ? "#2d5a10" : th.color }}
                        title={`Sort by ${col.label}`}
                        onClick={() => {
                          // Clicking the column you are already on flips it;
                          // a new column starts at ITS OWN default direction.
                          if (on) setSortDir(d => ((d || col.dir) === "desc" ? "asc" : "desc"));
                          else { setSortKey(key); setSortDir(null); }
                        }}>
                      {labels[i]}{on ? (dir === "desc" ? " ↓" : " ↑") : ""}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {shown.map(f => {
                const key = String(f.farmerId ?? f.farmerName);
                const isOpen = open.has(key);
                const last = f.policies.reduce((d, p) => (p.lastPaymentDate && (!d || p.lastPaymentDate > d) ? p.lastPaymentDate : d), null);
                return (
                  <React.Fragment key={key}>
                    <tr onClick={() => setOpen(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; })}
                        style={{ borderBottom: "1px solid #f0f4f0", cursor: "pointer" }}>
                      <td style={{ padding: "9px 10px", fontWeight: 700, color: "#1a2e1a" }}>
                        <span style={{ color: "#bbb", marginRight: 6 }}>{isOpen ? "▾" : "▸"}</span>{f.farmerName}
                        <span style={{ fontSize: 10, color: "#a8b2a6", marginLeft: 6 }}>
                          {f.policies.length} polic{f.policies.length === 1 ? "y" : "ies"}
                          {/* Named, never picked. A farm written by two agents says
                              so; stamping it with one would be wrong on 19 farms
                              and give no sign of it. */}
                          {(f.agents || []).length === 1 && <> · {f.agents[0]}</>}
                          {(f.agents || []).length > 1 && <> · <span title={f.agents.join(", ")}>{f.agents.length} agents</span></>}
                        </span>
                      </td>
                      <td style={td}>{fmtD0(f.billed)}</td>
                      <td style={{ ...td, color: f.paid > 0 ? "#2d5a10" : "#bbb" }}>{f.paid > 0 ? fmtD0(f.paid) : "—"}</td>
                      <td style={{ ...td, fontWeight: 800, color: f.owed > 0 ? "#1a2e1a" : f.credit < 0 ? "#9a5a1a" : "#60735f" }}>
                        {fmtD0(f.owed)}
                        {/* A credit on one crop year must never cancel a debt on
                            another — they are different bills. Shown beside, never
                            netted in. */}
                        {f.credit < 0 && <div style={{ fontSize: 10, color: "#9a5a1a", fontWeight: 600 }}>{fmtD0(f.credit)} credit</div>}
                      </td>
                      <td style={{ padding: "9px 10px", fontSize: 11.5, color: "#60735f" }}>{STATE_LABEL[f.state] || f.state}</td>
                      <td style={{ ...td, fontSize: 11.5, color: "#60735f" }}>{last || "—"}</td>
                    </tr>
                    {isOpen && f.policies.map(p => (
                      <tr key={p.policyId} style={{ background: "#fafcfa", borderBottom: "1px solid #f4f8f4" }}>
                        <td style={{ padding: "6px 10px 6px 28px", fontSize: 11.5, color: "#60735f" }}>
                          {p.policyNumber || "policy"}
                          <span style={{ fontSize: 10, color: "#a8b2a6", marginLeft: 6 }}>
                            {String(p.policyType || "").toUpperCase()} · CY{p.cropYear} · {agentLabel(p.agent)} · {p.basis}
                          </span>
                        </td>
                        <td style={{ ...td, fontSize: 11.5 }}>{fmtD0(p.billed)}</td>
                        <td style={{ ...td, fontSize: 11.5, color: p.paid > 0 ? "#2d5a10" : "#bbb" }}>
                          {p.paid > 0 ? fmtD0(p.paid) : "—"}
                          {p.paymentCount > 1 && <span style={{ fontSize: 10.5, color: "#a8b2a6", marginLeft: 4 }}>×{p.paymentCount}</span>}
                        </td>
                        <td style={{ ...td, fontSize: 11.5 }}>{fmtD0(p.outstanding)}</td>
                        <td style={{ padding: "6px 10px", fontSize: 11, color: "#60735f" }}>{STATE_LABEL[p.state] || p.state}</td>
                        <td style={{ ...td, fontSize: 11, color: "#60735f" }}>{p.lastPaymentDate || "—"}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {!shown.length && (
                <tr><td colSpan={6} style={{ padding: "18px 10px", textAlign: "center", color: "#60735f", fontSize: 13 }}>
                  {/* A blank table must name the filter that emptied it — otherwise
                      a mistyped search reads as "this agent's book is all paid". */}
                  {needle ? <>Nothing matches “{q}”{agent || year != null ? <> under the current filters</> : null}.</>
                   : (agent || year != null)
                     ? <>No {year != null ? <>CY{year} </> : null}
                         {agent === UNATTRIBUTED ? "unattributed policies" : agent ? agent + " policies" : "policies"}
                         {" "}{onlyOwing ? "show an outstanding balance" : "carry a bill or a payment"}.</>
                   : onlyOwing ? "Nobody is showing an outstanding balance."
                   : "No policies carry a bill or a payment."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* REFUSED, LISTED, AND EXCLUDED FROM THE FIGURE ABOVE — never silently
          dropped and never quietly counted. Measured 2026-08-27: only ONE of six
          DRP policies carrying a payment matches its producer premium, and the
          largest dairy in the book paid $940,800 against a $666,265 CY2025
          premium and $420,210 against a $530,322 CY2026 premium ON THE SAME DAY.
          The money does not divide the way the policy rows do, so an outstanding
          balance here would be an invented fact. */}
      {excluded.length > 0 && (
        <Card style={{ background: "#fdfaf4", borderColor: "#e8dcc4" }}>
          <SectionTitle style={{ margin: 0, border: "none", padding: 0, color: "#7a5a1a" }}>
            ⚠ Not reconciled — {excluded.length} DRP polic{excluded.length === 1 ? "y" : "ies"}
          </SectionTitle>
          <div style={{ fontSize: 12, color: "#7a5a1a", margin: "8px 0 10px", lineHeight: 1.55 }}>
            DRP premium payments do not align to a policy year — endorsements are quarterly and
            its crop year derives from the endorsement’s effective date, so a payment cannot be
            attributed from anything we hold. These are shown so nothing is hidden, and are
            <b> excluded from the outstanding figure</b> rather than turned into a balance we
            cannot stand behind.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <tbody>
              {excluded.map(p => (
                <tr key={p.policyId} style={{ borderTop: "1px solid #f0e8d8" }}>
                  <td style={{ padding: "6px 8px", color: "#7a5a1a" }}>
                    {p.farmerName} <span style={{ color: "#a89268" }}>· {p.policyNumber} · CY{p.cropYear}</span>
                  </td>
                  <td style={{ ...td, fontSize: 11.5, color: "#7a5a1a" }}>billed {fmtD0(p.billed)}</td>
                  <td style={{ ...td, fontSize: 11.5, color: "#7a5a1a" }}>paid {fmtD0(p.paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function PremiumAnalyticsView({ isMobile = false }) {
  const [facts, setFacts]     = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [paneTab, setPaneTab] = useState("analytics");
  const [closed, setClosed]   = useState(() => new Set());
  const [err, setErr]         = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [yearB, setYearB]     = useState(null);   // the year in focus
  const [yearA, setYearA]     = useState(null);   // what it's compared against
  const [prodSel, setProdSel] = useState(() => new Set());
  const [agentSel, setAgentSel] = useState(() => new Set());
  const [aipSel, setAipSel]     = useState(() => new Set());
  const [cropSel, setCropSel]   = useState(() => new Set());
  const [countySel, setCountySel] = useState(() => new Set());
  const [stateSel, setStateSel]   = useState(() => new Set());
  const [facetOpen, setFacetOpen] = useState(() => ({}));   // facet rows expanded past their cap
  const [basis, setBasis]     = useState("gross"); // gross | net
  const [groupBy, setGroupBy] = useState("product");
  const [topN, setTopN]       = useState(12);
  const [orient, setOrient]   = useState("vertical"); // vertical | horizontal
  const [moverOpen, setMoverOpen] = useState(() => ({}));  // "What moved" cards expanded, by title
  const [farmSort, setFarmSort]   = useState("b");         // By-farm sort column
  const [farmLimit, setFarmLimit] = useState(25);
  const [farmQuery, setFarmQuery] = useState("");          // By-farm name search

  const load = useCallback(async (fresh) => {
    setBusy(true);
    try {
      const opt = { fresh: !!fresh };
      const [policies, coverage, prfRows, lrpRows, drpRows, hist, farmerRows, m13, payRows, credRows] = await Promise.all([
        sbFetch("policies", { ...opt, select: "id,farmer_id,policy_number,crop_year,policy_type,aip,state,county,agent_name,agent_name_override" }).catch(() => []),
        sbFetch("crop_coverage", { ...opt, select: "policy_id,plan,mpci_plan_code,crop,county,acres,total_premium,subsidy,net_premium,liability,coverage_type,coverage_level,price_election_pct" }).catch(() => []),
        sbFetch("prf_policies", { ...opt, select: "policy_id,county,total_acres,total_premium,subsidy,net_premium,total_liability" }).catch(() => []),
        sbFetch("lrp_endorsements", { ...opt, select: "policy_id,effective_date,commodity,total_premium,subsidy,producer_premium,liability" }).catch(() => []),
        sbFetch("drp_endorsements", { ...opt, select: "policy_id,soi_crop_year,effective_date,total_premium,subsidy,producer_premium,liability" }).catch(() => []),
        sbFetch("historical_summaries", opt).catch(() => []),
        sbFetch("farmers", { ...opt, select: "id,name,metadata" }).catch(() => []),
        sbFetch("m13_import_log", { ...opt, order: "imported_at.desc,id.asc", filters: ["status=eq.success"] }).catch(() => []),
        // The grower's own premium payments (M13 P12), and the claim credits the
        // AIP already applied. `loss_notices.crop_year` is the payment-DATE year
        // and is deliberately not selected — payments join on policy_id, scoped by
        // `file_crop_year` (the M13 file year).
        sbFetch("loss_notices", { ...opt, select: "policy_id,cause_code,amount,loss_event_date,flag,file_crop_year" }).catch(() => []),
        sbFetch("indemnities", { ...opt, select: "policy_id,claim_number,loss_code,policy_type,crop_year,crop,county,claim_stages,indemnity_amount" }).catch(() => []),
      ]);
      const built = buildPremiumFacts({ policies, coverage, prfRows, lrpRows, drpRows, hist, farmerRows });
      setFacts(built);
      setOutstanding(buildOutstandingBook({ policies, coverage, prfRows, lrpRows, drpRows, farmerRows, payRows, credRows }));
      setClosed(new Set(hist.map(h => h.crop_year).filter(Boolean)));
      setSyncedAt(latestTs(m13, "imported_at"));
      if (!built.length) setErr("No premium rows came back. If you're signed in as an admin this means the RLS migration in sql/009 hasn't been applied yet.");
      else setErr(null);
    } catch (e) {
      setErr(String((e && e.message) || e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const years = useMemo(() => {
    if (!facts) return [];
    return [...new Set(facts.map(f => f.year).filter(Boolean))].sort((a, b) => b - a);
  }, [facts]);

  // Default to the MPCI crop year vs the one before it. Deliberately NOT the
  // newest year in the data: livestock rolls to the next reinsurance year on
  // July 1, so from July onward `years` already carries CY2027 built from a
  // handful of DRP/LRP endorsements and three rolled-over crop policies. Opening
  // on that read as a book that had lost almost everything — 42 PRF customers
  // sat in "Not yet reported" because CY2027 PRF isn't sellable until the
  // November sales closing. MPCI is the crop year the book is actually written
  // on, so it anchors the page; the newer livestock year is still selectable.
  useEffect(() => {
    if (!years.length) return;
    const anchor = years.includes(CURRENT_CROP_YEAR) ? CURRENT_CROP_YEAR : years[0];
    const prior = years.find(y => y < anchor);
    setYearB(prev => (prev != null && years.includes(prev) ? prev : anchor));
    setYearA(prev => (prev != null && years.includes(prev) ? prev : (prior != null ? prior : anchor)));
  }, [years]);

  // A snapshot year is authoritative for everything it holds. The snapshot never
  // carried DRP (it spans crop years), so DRP always comes from the live table —
  // taking both would double-count every other product in a closed year.
  const inYear = useCallback((f, y) => {
    if (f.year !== y) return false;
    if (!closed.has(y)) return f.src === "live";
    return f.src === "hist" || f.product === "DRP";
  }, [closed]);

  const scoped = useMemo(() => {
    if (!facts) return [];
    const dCrop = PA_DIMS.find(d => d.key === "crop").get;
    const dCounty = PA_DIMS.find(d => d.key === "county").get;
    const dState = PA_DIMS.find(d => d.key === "state").get;
    return facts.filter(f =>
      (!prodSel.size   || prodSel.has(f.product)) &&
      (!agentSel.size  || agentSel.has(f.agent || PA_UNATTRIBUTED)) &&
      (!aipSel.size    || aipSel.has(f.aip || "—")) &&
      (!cropSel.size   || cropSel.has(dCrop(f))) &&
      (!stateSel.size  || stateSel.has(dState(f))) &&
      (!countySel.size || countySel.has(dCounty(f))));
  }, [facts, prodSel, agentSel, aipSel, cropSel, stateSel, countySel]);

  const rowsA = useMemo(() => scoped.filter(f => inYear(f, yearA)), [scoped, yearA, inYear]);
  const rowsB = useMemo(() => scoped.filter(f => inYear(f, yearB)), [scoped, yearB, inYear]);

  const val = useCallback((rows) => paSum(rows, basis), [basis]);

  const agents = useMemo(() => {
    if (!facts) return [];
    const seen = new Set(facts.map(f => f.agent || PA_UNATTRIBUTED));
    const known = AGENCY_AGENTS.filter(a => seen.has(a));
    return seen.has(PA_UNATTRIBUTED) ? [...known, PA_UNATTRIBUTED] : known;
  }, [facts]);

  // Facet options come from the data in view, not a hardcoded list, so a new
  // county or carrier becomes filterable the moment it lands in the book.
  const facetOpts = useCallback((dimKey) => {
    if (!facts) return [];
    const g = PA_DIMS.find(d => d.key === dimKey).get;
    let both = facts.filter(f => f.year === yearA || f.year === yearB);
    // DRP/LRP/PRF have no crop of their own — the "crop" on those facts is just
    // the product restated (DRP→MILK, LRP→FED/FEEDER CATTLE, PRF→PASTURE,
    // RANGELAND, FORAGE). Listing them under Crop duplicates the Product row and
    // makes two chips that do exactly the same thing. Filter by Product instead.
    if (dimKey === "crop") both = both.filter(f => !PA_PRODUCT_IS_ITS_OWN_CROP.has(f.product));
    return [...new Set(both.map(g))].filter(v => v && v !== "—").sort();
  }, [facts, yearA, yearB]);

  // Roll the two years up on any dimension. High-cardinality dimensions get a
  // Top-N with the tail folded into one "Other" row rather than truncated —
  // a silently dropped tail reads as "this is everything" when it isn't.
  const groupRows = useCallback((dimKey, limit) => {
    const g = PA_DIMS.find(d => d.key === dimKey).get;
    const m = new Map();
    const add = (rows, side) => rows.forEach(f => {
      const k = g(f) || "—";
      const cur = m.get(k) || { key: k, a: 0, b: 0 };
      cur[side] += paNum(f[basis]);
      m.set(k, cur);
    });
    add(rowsA, "a");
    add(rowsB, "b");
    let all = [...m.values()]
      .filter(r => r.a || r.b)
      .sort((x, y) => (y.b - x.b) || (y.a - x.a));
    if (limit && all.length > limit) {
      const tail = all.slice(limit);
      let ta = 0, tb = 0;
      tail.forEach(r => { ta += r.a; tb += r.b; });
      all = [...all.slice(0, limit), { key: `Other (${tail.length})`, a: ta, b: tb }];
    }
    return all.map(r => ({ ...r, d: r.b - r.a, pct: paPct(r.b, r.a) }));
  }, [rowsA, rowsB, basis]);

  const chartRows = useMemo(() => groupRows(groupBy, topN), [groupRows, groupBy, topN]);

  const byProduct = useMemo(() => {
    const order = [...PA_PRODUCTS, "OTHER", "UNKNOWN"];
    return order.map(p => {
      const a = val(rowsA.filter(f => f.product === p));
      const b = val(rowsB.filter(f => f.product === p));
      return { key: p, a, b, d: b - a, pct: paPct(b, a) };
    }).filter(r => r.a || r.b);
  }, [rowsA, rowsB, val]);

  const byAgent = useMemo(() => agents.map(name => {
    const match = (f) => (f.agent || PA_UNATTRIBUTED) === name;
    const a = val(rowsA.filter(match));
    const b = val(rowsB.filter(match));
    return { key: name, a, b, d: b - a, pct: paPct(b, a) };
  }).filter(r => r.a || r.b).sort((x, y) => y.b - x.b), [agents, rowsA, rowsB, val]);

  // Customer-level movers — the "what grew, what fell" answer. Split four ways
  // because "fell to zero" and "fell 20%" are different conversations.
  // Prior-year premium per customer for the WHOLE BOOK, ignoring the current
  // filters. Needed because "no premium last year" under a filter does not mean
  // new business: with Agent=Kevin Keenan selected, Mulligan Family Farm read as
  // new at $12,808 when they billed $106,730 in 2025 — all of it under Mike.
  // What actually changed is that Kevin wrote their LRP line. Calling that new
  // business overstates growth and hides an agent-to-agent shift.
  const priorBookByCust = useMemo(() => {
    const m = new Map();
    if (!facts) return m;
    facts.filter(f => inYear(f, yearA)).forEach(f => {
      if (!f.custKey) return;
      m.set(f.custKey, (m.get(f.custKey) || 0) + paNum(f[basis]));
    });
    return m;
  }, [facts, yearA, inYear, basis]);

  const filtersOn = !!(prodSel.size || agentSel.size || aipSel.size || cropSel.size || stateSel.size || countySel.size);

  const movers = useMemo(() => {
    const m = new Map();
    const add = (rows, side) => rows.forEach(f => {
      if (!f.custKey) return;
      const cur = m.get(f.custKey) || { key: f.custKey, name: f.customer, agent: f.agent, a: 0, b: 0 };
      cur[side] += paNum(f[basis]);
      if (side === "b") { cur.name = f.customer; cur.agent = f.agent; }
      m.set(f.custKey, cur);
    });
    add(rowsA, "a");
    add(rowsB, "b");
    const all = [...m.values()].map(r => ({ ...r, d: r.b - r.a, pct: paPct(r.b, r.a) }));
    return {
      grew:    all.filter(r => r.a > 0 && r.d > 0).sort((x, y) => y.d - x.d),
      fell:    all.filter(r => r.a > 0 && r.b > 0 && r.d < 0).sort((x, y) => x.d - y.d),
      // Truly new: nothing last year under this filter AND nothing last year
      // anywhere in the book.
      gained:  all.filter(r => r.a === 0 && r.b > 0 && !(priorBookByCust.get(r.key) > 0)).sort((x, y) => y.b - x.b),
      // Existing customer, new to this view — they billed last year, just not
      // under the filters currently applied.
      shifted: all.filter(r => r.a === 0 && r.b > 0 && (priorBookByCust.get(r.key) > 0))
                  .map(r => ({ ...r, priorBook: priorBookByCust.get(r.key) || 0 }))
                  .sort((x, y) => y.b - x.b),
      lost:    all.filter(r => r.a > 0 && r.b === 0).sort((x, y) => y.a - x.a),
    };
  }, [rowsA, rowsB, basis, priorBookByCust]);

  // ── BY FARM × PRODUCT ────────────────────────────────────────────────────
  // One row per customer, one column per product, so you can see how a single
  // farm is built — MPCI vs the SCO/ECO layers on top of it, plus PRF/LRP/DRP —
  // instead of inferring it from the product and agent rollups separately.
  // Columns are the products actually present under the current filters; a book
  // with no STAX doesn't get an empty STAX column.
  const byFarm = useMemo(() => {
    const m = new Map();
    const add = (rows, side) => rows.forEach(f => {
      if (!f.custKey) return;
      const cur = m.get(f.custKey) || { key: f.custKey, name: f.customer, agent: f.agent, prod: {}, a: 0, b: 0 };
      const v = paNum(f[basis]);
      if (side === "b") {
        cur.prod[f.product] = (cur.prod[f.product] || 0) + v;
        cur.b += v;
        cur.name = f.customer; cur.agent = f.agent;
      } else cur.a += v;
      m.set(f.custKey, cur);
    });
    add(rowsA, "a");
    add(rowsB, "b");
    const rows = [...m.values()].map(r => ({ ...r, d: r.b - r.a, pct: paPct(r.b, r.a) }));
    const cols = PA_PRODUCT_ORDER.filter(p => rows.some(r => (r.prod[p] || 0) !== 0));
    return { rows, cols };
  }, [rowsA, rowsB, basis]);

  const openYear = yearB != null && !closed.has(yearB);
  const grossA = paSum(rowsA, "gross"), grossB = paSum(rowsB, "gross");
  const netA   = paSum(rowsA, "net"),   netB   = paSum(rowsB, "net");
  const custA  = new Set(rowsA.map(f => f.custKey)).size;
  const custB  = new Set(rowsB.map(f => f.custKey)).size;

  const card    = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 12, padding: isMobile ? 14 : 18 };
  const th      = { textAlign: "right", padding: "7px 10px", fontSize: 10, fontWeight: 800, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" };
  const thL     = { ...th, textAlign: "left" };
  const td      = { textAlign: "right", padding: "8px 10px", fontSize: 13, color: "#1a2e1a", whiteSpace: "nowrap", borderTop: "1px solid #eef3ee" };
  const tdL     = { ...td, textAlign: "left", fontWeight: 600 };
  const scroller = { overflowX: "auto", WebkitOverflowScrolling: "touch" };
  const chipBtn = (on) => ({ padding: "5px 11px", borderRadius: 999, border: "1px solid " + (on ? "#2d6a2d" : "#dde7dd"), background: on ? "#2d6a2d" : "#fff", color: on ? "#fff" : "#4a6e4a", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" });
  const toggle = (setter) => (k) => setter(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  if (err && !facts) return <div style={{ ...card, color: "#a33" }}>{err}</div>;
  if (!facts) return <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}><UiSpinner />Loading premium data…</div>;

  const kpi = (label, now, before, fmtFn) => {
    const d = now - before;
    return (
      <div key={label} style={{ ...card, flex: "1 1 170px", minWidth: 150 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: "#1a2e1a", marginTop: 5, fontFamily: "'Lora',serif" }}>{fmtFn(now)}</div>
        <div style={{ fontSize: 12, marginTop: 3, color: paSignColor(d), fontWeight: 700 }}>
          {d === 0 ? "no change" : (d > 0 ? "▲ " : "▼ ") + fmtFn(Math.abs(d))} <span style={{ color: "#60735f", fontWeight: 500 }}>vs {yearA}</span>
        </div>
      </div>
    );
  };

  const cmpTable = (rows, headLabel, noteFor) => {
    const max = rows.reduce((m, r) => Math.max(m, r.a, r.b), 0);
    return (
      <div style={scroller}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
          <thead><tr>
            <th style={thL}>{headLabel}</th>
            <th style={th}>{yearA}</th>
            <th style={th}>{yearB}</th>
            <th style={th}>Change</th>
            <th style={th}>%</th>
            <th style={{ ...th, width: 120 }}>{yearA} ▪ {yearB}</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}>
                <td style={tdL} title={noteFor ? noteFor(r.key) : undefined}>{r.key}</td>
                <td style={{ ...td, color: "#60735f" }}>{fmtD0(Math.round(r.a))}</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmtD0(Math.round(r.b))}</td>
                <td style={{ ...td, color: paSignColor(r.d), fontWeight: 700 }}>{paDeltaStr(r.d)}</td>
                <td style={{ ...td, color: paSignColor(r.d) }}>{paPctStr(r.pct)}</td>
                <td style={{ ...td, padding: "8px 10px" }}><PABar before={r.a} now={r.b} max={max} /></td>
              </tr>
            ))}
            <tr>
              <td style={{ ...tdL, borderTop: "2px solid #dde7dd", fontWeight: 800 }}>Total</td>
              <td style={{ ...td, borderTop: "2px solid #dde7dd", color: "#60735f" }}>{fmtD0(Math.round(rows.reduce((s, r) => s + r.a, 0)))}</td>
              <td style={{ ...td, borderTop: "2px solid #dde7dd", fontWeight: 800 }}>{fmtD0(Math.round(rows.reduce((s, r) => s + r.b, 0)))}</td>
              <td style={{ ...td, borderTop: "2px solid #dde7dd", fontWeight: 800, color: paSignColor(rows.reduce((s, r) => s + r.d, 0)) }}>{paDeltaStr(rows.reduce((s, r) => s + r.d, 0))}</td>
              <td style={{ ...td, borderTop: "2px solid #dde7dd" }} colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const moverList = (title, rows, field, tone, emptyMsg) => (
    <div style={{ ...card, flex: "1 1 320px", minWidth: 280 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: tone, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
        {title} <span style={{ color: "#60735f", fontWeight: 600 }}>({rows.length})</span>
      </div>
      {!rows.length ? <div style={{ fontSize: 12, color: "#677167" }}>{emptyMsg}</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {(moverOpen[title] ? rows : rows.slice(0, 12)).map(r => (
            <div key={r.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5 }}>
              <span style={{ color: "#1a2e1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={(r.agent || "no agent on file") + " · " + r.name}>{r.name}</span>
              <span style={{ color: tone, fontWeight: 700, whiteSpace: "nowrap" }}>
                {field === "d" ? paDeltaStr(r.d) : fmtD0(Math.round(r[field]))}
              </span>
            </div>
          ))}
          {rows.length > 12 && (
            <button
              type="button"
              onClick={() => setMoverOpen(o => ({ ...o, [title]: !o[title] }))}
              style={{ marginTop: 4, alignSelf: "flex-start", background: "none", border: "none", padding: 0,
                       font: "inherit", fontSize: 11.5, fontWeight: 700, color: tone, cursor: "pointer" }}>
              {moverOpen[title] ? "− show less" : `+${rows.length - 12} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
        <h2 style={{ ...uiPageTitle(isMobile) }}>Premium Analytics</h2>
        <span style={{ background: "#1a2e1a", color: "#fff", fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", padding: "3px 9px", borderRadius: 999 }}>🔒 CONFIDENTIAL</span>
        <div style={{ flex: 1 }} />
        <FreshAsOf date={syncedAt} label="AIP sync" prefix="synced" />
        <button onClick={() => load(true)} disabled={busy} style={{ ...chipBtn(false), opacity: busy ? 0.5 : 1 }}>
          {busy ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      <div style={{ fontSize: 12.5, color: "#4a6e4a", marginTop: -4 }}>
        Gross premium is producer premium plus RMA subsidy — the same basis both years are stored on. Visible to Kevin and Mike only.
      </div>

      {err && <div style={{ ...card, borderColor: "#e6c9c9", background: "#fdf6f6", color: "#a33", fontSize: 13 }}>{err}</div>}

      <div style={{ display: "flex", gap: 8, borderBottom: "1px solid #e4ece4", paddingBottom: 2 }}>
        {[["analytics", "Premium analytics"], ["outstanding", "Outstanding premium"]].map(([k, label]) => (
          <button key={k} onClick={() => setPaneTab(k)}
            style={{ fontFamily: "inherit", fontSize: 13, fontWeight: paneTab === k ? 800 : 600,
                     color: paneTab === k ? "#1a2e1a" : "#60735f", background: "none", border: "none",
                     borderBottom: paneTab === k ? "2px solid #2d5a10" : "2px solid transparent",
                     padding: "7px 4px", marginBottom: -3, cursor: "pointer" }}>
            {label}
          </button>
        ))}
      </div>

      {paneTab === "outstanding" && <OutstandingPremiumPane book={outstanding} />}

      {paneTab === "analytics" && (<>

      {/* Year pair + basis */}
      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#4a6e4a" }}>Compare&nbsp;
          <select value={yearA == null ? "" : yearA} onChange={e => setYearA(Number(e.target.value))}
            style={{ fontFamily: "inherit", fontSize: 13, padding: "5px 8px", borderRadius: 7, border: "1px solid #dde7dd" }}>
            {years.map(y => <option key={y} value={y}>{y}{closed.has(y) ? "" : " (open)"}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#4a6e4a" }}>to&nbsp;
          <select value={yearB == null ? "" : yearB} onChange={e => setYearB(Number(e.target.value))}
            style={{ fontFamily: "inherit", fontSize: 13, padding: "5px 8px", borderRadius: 7, border: "1px solid #dde7dd" }}>
            {years.map(y => <option key={y} value={y}>{y}{closed.has(y) ? "" : " (open)"}</option>)}
          </select>
        </label>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setBasis("gross")} style={chipBtn(basis === "gross")}>Gross premium</button>
          <button onClick={() => setBasis("net")} style={chipBtn(basis === "net")}>Producer premium</button>
        </div>
      </div>

      {basis === "net" && (() => {
        // Producer premium only. Gross is unaffected by this and stays trustworthy.
        const bad = [...rowsA, ...rowsB].filter(f => f.netOk === false);
        if (!bad.length) return null;
        const badA = rowsA.filter(f => f.netOk === false), badB = rowsB.filter(f => f.netOk === false);
        return (
          <div style={{ ...card, background: "#fdf6f6", borderColor: "#e6c9c9", fontSize: 12.5, color: "#8a3a3a", lineHeight: 1.55 }}>
            <b>Producer premium is understated.</b> {bad.length} coverage{bad.length === 1 ? "" : "s"} carry a gross
            premium with no subsidy/producer split, so they add to gross but contribute nothing here
            — {fmtD0(Math.round(paSum(badA, "gross")))} of gross in {yearA} and {fmtD0(Math.round(paSum(badB, "gross")))} in {yearB}.
            Nearly all are the hand-loaded books (Agri Sompo, ProAg), where the split lives on the MPCI Statement of
            Accounts and was never captured. The split can’t be derived from what’s stored, so it is left visible rather
            than guessed. <b>Gross premium is unaffected</b> — switch back to it for a figure that ties to the AIP files.
          </div>
        );
      })()}

      {openYear && (
        <div style={{ ...card, background: "#fffbe9", borderColor: "#e8dca8", fontSize: 12.5, color: "#6b5a1e", lineHeight: 1.55 }}>
          <b>Both years are still moving.</b> CY{yearB} is assembled live from the daily R&amp;H and NAU syncs and grows as
          policies are written and acreage is keyed. CY{yearA} is the end-of-year snapshot, but it is still being
          reconciled and back-filled by hand — the books with no SFTP feed land there last. So read a gap either way as
          “not in yet” rather than as a real move, and re-check a number that surprises you. Nothing here is cached:
          every figure is recomputed from the tables on load, so both years correct themselves as the work lands.
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {kpi("Gross premium", grossB, grossA, (v) => fmtD0(Math.round(v)))}
        {kpi("Producer premium", netB, netA, (v) => fmtD0(Math.round(v)))}
        {kpi("Customers", custB, custA, (v) => fmt(v))}
      </div>

      {/* Filters */}
      <div style={{ ...card, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>Product</span>
          {PA_PRODUCTS.map(p => <button key={p} onClick={() => toggle(setProdSel)(p)} style={chipBtn(prodSel.has(p))} title={PA_PRODUCT_NOTE[p]}>{p}</button>)}
          {!!prodSel.size && <button onClick={() => setProdSel(new Set())} style={{ ...chipBtn(false), borderStyle: "dashed" }}>clear</button>}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>Agent</span>
          {agents.map(a => <button key={a} onClick={() => toggle(setAgentSel)(a)} style={chipBtn(agentSel.has(a))}>{a}</button>)}
          {!!agentSel.size && <button onClick={() => setAgentSel(new Set())} style={{ ...chipBtn(false), borderStyle: "dashed" }}>clear</button>}
        </div>
        {[["Carrier", "aip", aipSel, setAipSel], ["State", "state", stateSel, setStateSel], ["Crop", "crop", cropSel, setCropSel], ["County", "county", countySel, setCountySel]].map(([label, dim, sel, setSel]) => {
          const opts = facetOpts(dim);
          if (!opts.length) return null;
          // County runs to ~50 values; collapse to the ones already chosen plus a
          // count until the user opens it, so the panel doesn't become a wall.
          // County runs to ~85 values. Collapse it so the panel isn't a wall, but
          // the overflow has to be OPENABLE — it used to say "pick one to filter",
          // which is impossible when the one you want is inside the hidden 69.
          const collapsible = dim === "county" && opts.length > 14 && !sel.size && !facetOpen[dim];
          const shown = collapsible ? opts.slice(0, 14) : opts;
          return (
            <div key={dim} style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>{label}</span>
              {shown.map(o => <button key={o} onClick={() => toggle(setSel)(o)} style={chipBtn(sel.has(o))}>{o}</button>)}
              {shown.length < opts.length && (
                <button type="button" onClick={() => setFacetOpen(o => ({ ...o, [dim]: true }))}
                  style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: 11,
                           fontWeight: 700, color: "#2d6a2d", cursor: "pointer" }}>
                  +{opts.length - shown.length} more — show all
                </button>
              )}
              {facetOpen[dim] && opts.length > 14 && (
                <button type="button" onClick={() => setFacetOpen(o => ({ ...o, [dim]: false }))}
                  style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: 11,
                           color: "#677167", cursor: "pointer" }}>show fewer</button>
              )}
              {!!sel.size && <button onClick={() => setSel(new Set())} style={{ ...chipBtn(false), borderStyle: "dashed" }}>clear</button>}
            </div>
          );
        })}
        {!!(prodSel.size || agentSel.size || aipSel.size || cropSel.size || stateSel.size || countySel.size) && (
          <div>
            <button onClick={() => { setProdSel(new Set()); setAgentSel(new Set()); setAipSel(new Set()); setCropSel(new Set()); setStateSel(new Set()); setCountySel(new Set()); }}
              style={{ ...chipBtn(false), borderStyle: "dashed", color: "#a33", borderColor: "#e6c9c9" }}>
              reset all filters
            </button>
          </div>
        )}
      </div>

      {/* ── Chart ─────────────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
            {basis === "gross" ? "Gross premium" : "Producer premium"} by {(PA_DIMS.find(d => d.key === groupBy) || {}).label.toLowerCase()}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setOrient("vertical")} style={chipBtn(orient === "vertical")} title="Columns side by side">▮▮ Columns</button>
            <button onClick={() => setOrient("horizontal")} style={chipBtn(orient === "horizontal")} title="Better for long names and long lists">▬ Bars</button>
          </div>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#4a6e4a" }}>Group by&nbsp;
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
              style={{ fontFamily: "inherit", fontSize: 13, padding: "5px 8px", borderRadius: 7, border: "1px solid #dde7dd" }}>
              {PA_DIMS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#4a6e4a" }}>Show&nbsp;
            <select value={topN} onChange={e => setTopN(Number(e.target.value))}
              style={{ fontFamily: "inherit", fontSize: 13, padding: "5px 8px", borderRadius: 7, border: "1px solid #dde7dd" }}>
              {[8, 12, 20, 40, 0].map(n => <option key={n} value={n}>{n === 0 ? "All" : "Top " + n}</option>)}
            </select>
          </label>
        </div>
        {orient === "vertical"
          ? <PAColumnChart rows={chartRows} yearA={yearA} yearB={yearB} isMobile={isMobile} />
          : <PAChart rows={chartRows} yearA={yearA} yearB={yearB} isMobile={isMobile} />}
        <div style={{ fontSize: 11, color: "#677167", marginTop: 12 }}>
          Sorted by {yearB}. Every bar shares one scale, so heights compare across groups as well as within them.
          Hover any bar for its exact figure. Grouping by customer or county puts a lot of names on the axis —
          switch to Bars for those.
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 10 }}>By product</div>
        {cmpTable(byProduct, "Product", (k) => PA_PRODUCT_NOTE[k] || "")}
      </div>

      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 4 }}>By agent</div>
        <div style={{ fontSize: 11.5, color: "#60735f", marginBottom: 10 }}>
          Written-agent names are collapsed to the real person — the book stores one agent under many spellings
          (“MICHAEL J MEISENZAHL”, “Meisenzahl, Michael J”, the R&amp;H code “5727-03”). Policies written by a prior
          or outside agency carry that agency’s name and fall under {PA_UNATTRIBUTED}.
          <br />
          Where you’ve set an Agent of Record by hand, that assignment is applied to the policy’s earlier years too,
          so a book moved to the shared account doesn’t read as one agent losing it and another inventing it. It is
          matched per policy, not per farm — most of these customers hold some policies that were not reassigned.
        </div>
        {cmpTable(byAgent, "Agent")}
      </div>

      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 10 }}>Agent × product — {yearB}{openYear ? " (open)" : ""}</div>
        <div style={scroller}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead><tr>
              <th style={thL}>Agent</th>
              {byProduct.map(p => <th key={p.key} style={th}>{p.key}</th>)}
              <th style={th}>Total</th>
            </tr></thead>
            <tbody>
              {byAgent.map(a => {
                const mine = rowsB.filter(f => (f.agent || PA_UNATTRIBUTED) === a.key);
                return (
                  <tr key={a.key}>
                    <td style={tdL}>{a.key}</td>
                    {byProduct.map(p => {
                      const v = val(mine.filter(f => f.product === p.key));
                      return <td key={p.key} style={{ ...td, color: v ? "#1a2e1a" : "#c9d8c9" }}>{v ? fmtD0(Math.round(v)) : "—"}</td>;
                    })}
                    <td style={{ ...td, fontWeight: 800 }}>{fmtD0(Math.round(a.b))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── BY FARM × PRODUCT ─────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
            By farm — {yearB} {basis === "net" ? "net" : "gross"} premium by product
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              value={farmQuery}
              onChange={e => setFarmQuery(e.target.value)}
              placeholder="Find a farm…"
              style={{ padding: "5px 11px", borderRadius: 999, border: "1px solid #dde7dd", fontSize: 12,
                       fontFamily: "inherit", color: "#1a2e1a", outline: "none", width: 150 }} />
            <button type="button" onClick={() => setFarmSort("b")} style={chipBtn(farmSort === "b")}>Largest</button>
            <button type="button" onClick={() => setFarmSort("d")} style={chipBtn(farmSort === "d")}>Biggest change</button>
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: "#60735f", marginBottom: 10 }}>
          How each farm is built: base MPCI and the SCO/ECO layers written on top of it, beside their standalone
          lines. A blank cell means the farm carries none of that product — not that it is missing data.
        </div>
        {!byFarm.rows.length ? <div style={{ fontSize: 12, color: "#677167" }}>No customers under the current filters.</div> : (() => {
          // A name search bypasses the row cap entirely — the table is capped at 25
          // and the book runs past 250, so a mid-sized customer is far down the list
          // and reads as "missing" when you go looking for one by name.
          const q = farmQuery.trim().toLowerCase();
          const matched = q ? byFarm.rows.filter(r => String(r.name || "").toLowerCase().includes(q)) : byFarm.rows;
          if (!matched.length) return <div style={{ fontSize: 12, color: "#677167" }}>No farm matches “{farmQuery}”.</div>;
          const sorted = [...matched].sort((x, y) =>
            farmSort === "d" ? Math.abs(y.d) - Math.abs(x.d) : y.b - x.b);
          const shown = q ? sorted : sorted.slice(0, farmLimit);
          const tot = (p) => byFarm.rows.reduce((s, r) => s + (r.prod[p] || 0), 0);
          return (
            <>
              <div style={scroller}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
                  <thead>
                    <tr>
                      <th style={thL}>Farm</th>
                      {byFarm.cols.map(p => <th key={p} style={th} title={PA_PRODUCT_NOTE[p] || p}>{p}</th>)}
                      <th style={th}>Total</th>
                      <th style={th}>vs {yearA}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map(r => (
                      <tr key={r.key}>
                        <td style={tdL} title={(r.agent || "no agent on file") + " · " + r.name}>{r.name}</td>
                        {byFarm.cols.map(p => (
                          <td key={p} style={{ ...td, color: r.prod[p] ? "#1a2e1a" : "#c8d4c8" }}>
                            {r.prod[p] ? fmtD0(Math.round(r.prod[p])) : "—"}
                          </td>
                        ))}
                        <td style={{ ...td, fontWeight: 800 }}>{fmtD0(Math.round(r.b))}</td>
                        <td style={{ ...td, color: paSignColor(r.d), fontWeight: 700 }}>
                          {r.a === 0 ? <span style={{ color: "#8a7320" }}>new</span> : paDeltaStr(r.d)}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...tdL, borderTop: "2px solid #dde7dd", fontWeight: 800 }}>
                        Total <span style={{ color: "#60735f", fontWeight: 600 }}>({byFarm.rows.length} farms)</span>
                      </td>
                      {byFarm.cols.map(p => (
                        <td key={p} style={{ ...td, borderTop: "2px solid #dde7dd", fontWeight: 800 }}>{fmtD0(Math.round(tot(p)))}</td>
                      ))}
                      <td style={{ ...td, borderTop: "2px solid #dde7dd", fontWeight: 800 }}>
                        {fmtD0(Math.round(byFarm.rows.reduce((s, r) => s + r.b, 0)))}
                      </td>
                      <td style={{ ...td, borderTop: "2px solid #dde7dd", fontWeight: 800, color: paSignColor(byFarm.rows.reduce((s, r) => s + r.d, 0)) }}>
                        {paDeltaStr(byFarm.rows.reduce((s, r) => s + r.d, 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {byFarm.rows.length > farmLimit && (
                <button type="button" onClick={() => setFarmLimit(n => n + 50)}
                  style={{ marginTop: 10, background: "none", border: "none", padding: 0, font: "inherit",
                           fontSize: 12, fontWeight: 700, color: "#2d6a2d", cursor: "pointer" }}>
                  +{byFarm.rows.length - farmLimit} more farms
                </button>
              )}
              {farmLimit > 25 && (
                <button type="button" onClick={() => setFarmLimit(25)}
                  style={{ marginTop: 10, marginLeft: 14, background: "none", border: "none", padding: 0, font: "inherit",
                           fontSize: 12, fontWeight: 700, color: "#60735f", cursor: "pointer" }}>
                  show less
                </button>
              )}
            </>
          );
        })()}
      </div>

      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 8 }}>
          What moved — {yearA} → {yearB}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {moverList("Grew", movers.grew, "d", "#2d6a2d", "No customer grew.")}
          {moverList("Declined", movers.fell, "d", "#a33", "No customer declined.")}
          {moverList("New business", movers.gained, "b", "#2d6a2d", "No new customers.")}
          {!!movers.shifted.length && moverList("New to this view", movers.shifted, "b", "#8a7320", "")}
          {moverList(openYear ? "Not yet reported" : "Lost", movers.lost, "a", openYear ? "#8a7320" : "#a33",
            openYear ? "Everyone from " + yearA + " has reported." : "No customers lost.")}
        </div>
        {/* New business, isolated by agent — who actually brought in the year's
            genuinely new customers. Built from movers.gained, which is already
            the strict definition: nothing last year under these filters AND
            nothing last year anywhere in the book. "New to this view" is
            deliberately excluded, because that is a book moving between agents,
            not new money. */}
        {!!movers.gained.length && (() => {
          const m = new Map();
          movers.gained.forEach(r => {
            const k = r.agent || "Unattributed";
            const cur = m.get(k) || { agent: k, n: 0, dollars: 0, names: [] };
            cur.n++; cur.dollars += r.b; cur.names.push(r.name);
            m.set(k, cur);
          });
          const bookByAgent = new Map();
          rowsB.forEach(f => bookByAgent.set(f.agent || "Unattributed",
            (bookByAgent.get(f.agent || "Unattributed") || 0) + paNum(f[basis])));
          const rows = [...m.values()].sort((x, y) => y.dollars - x.dollars);
          const totN = rows.reduce((s, r) => s + r.n, 0);
          const totD = rows.reduce((s, r) => s + r.dollars, 0);
          return (
            <div style={{ ...card, marginTop: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 4 }}>
                New business by agent — {yearB}
              </div>
              <div style={{ fontSize: 11.5, color: "#60735f", marginBottom: 10 }}>
                Customers with nothing in {yearA} anywhere in the book. Excludes customers who merely
                moved between agents — those sit under “New to this view”.
              </div>
              <div style={scroller}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
                  <thead>
                    <tr>
                      <th style={thL}>Agent</th>
                      <th style={th}>New customers</th>
                      <th style={th}>New premium</th>
                      <th style={th}>Avg each</th>
                      <th style={th}>% of their {yearB} book</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const book = bookByAgent.get(r.agent) || 0;
                      return (
                        <tr key={r.agent}>
                          <td style={tdL} title={r.names.slice(0, 20).join(", ")}>{r.agent}</td>
                          <td style={td}>{r.n}</td>
                          <td style={{ ...td, fontWeight: 800, color: "#2d6a2d" }}>{fmtD0(Math.round(r.dollars))}</td>
                          <td style={{ ...td, color: "#60735f" }}>{fmtD0(Math.round(r.dollars / r.n))}</td>
                          <td style={td}>{book > 0 ? paPctStr((r.dollars / book) * 100) : "—"}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td style={{ ...tdL, borderTop: "2px solid #dde7dd", fontWeight: 800 }}>Total</td>
                      <td style={{ ...td, borderTop: "2px solid #dde7dd", fontWeight: 800 }}>{totN}</td>
                      <td style={{ ...td, borderTop: "2px solid #dde7dd", fontWeight: 800, color: "#2d6a2d" }}>{fmtD0(Math.round(totD))}</td>
                      <td style={{ ...td, borderTop: "2px solid #dde7dd" }} colSpan={2} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {!!movers.shifted.length && (
          <div style={{ fontSize: 11.5, color: "#60735f", marginTop: 8 }}>
            <b style={{ color: "#8a7320" }}>New to this view</b> is not new business. These {movers.shifted.length} billed
            {" "}{fmtD0(Math.round(movers.shifted.reduce((s, r) => s + r.priorBook, 0)))} across the book in {yearA} — just
            not under the {filtersOn ? "filters you have applied" : "current selection"}. Under an Agent filter that
            usually means another agent wrote them last year and this one wrote a line this year, which is a shift
            between agents rather than growth. They are kept out of New business so the total doesn’t overstate it.
          </div>
        )}
        {openYear && !!movers.lost.length && (
          <div style={{ fontSize: 11.5, color: "#60735f", marginTop: 8 }}>
            These {movers.lost.length} carried premium in {yearA} and have nothing in {yearB} yet. While {yearB} is open
            that usually means the policy hasn’t been keyed or reported — confirm before treating any of them as lost business.
          </div>
        )}
      </div>

      <div style={{ ...card, fontSize: 11.5, color: "#60735f", lineHeight: 1.6 }}>
        <b style={{ color: "#4a6e4a" }}>Where these numbers come from.</b> Closed years read
        <code style={{ background: "#f2f6f2", padding: "1px 4px", borderRadius: 4 }}>historical_summaries</code>, the frozen
        end-of-year snapshot. Open years are built live from crop_coverage, prf_policies, lrp_endorsements and
        drp_endorsements, so they track the daily syncs with no rebuild step. DRP always comes from the live table because
        the snapshot never carried it, and DRP/LRP crop years are taken from the endorsement effective date (livestock
        rolls July 1), not the policy row. Demo accounts are excluded, and so is business a customer transferred in under a
        prior agency’s policy numbers — that premium is their history, not ours. The page opens on CY{CURRENT_CROP_YEAR},
        the MPCI crop year the book is written on; livestock has already rolled to the next reinsurance year, so picking
        that year shows a handful of DRP/LRP endorsements and nothing else. SCO includes RMA plan ARPI. Customers are matched
        across years first by the AIP’s own policy number with the crop year stripped, and where that can’t decide, by the
        book’s canonical name key — which ignores case, word order, entity suffix and middle initials. A policy number
        claimed by two different customers is refused rather than guessed, so a customer the AIP spelled differently in
        two years and who also changed policy numbers can still show up under both New business and Lost. Matching those
        by guess is how eight customers got duplicated across nineteen rows once before.
      </div>
      </>)}
    </div>
  );
}

// ── QUICK SWITCHER (Ctrl+K / Cmd+K, or "/") ───────────────────────────────────
// Jump to any customer or page from anywhere in the agent app, instead of hunting
// through 33 sidebar links or scrolling 282 growers. Customers are grouped by the
// SAME name key the All Customers list uses (sortNameKey), so a grower with three
// policy cards is one result and opens on the same card that list would open.
// A customer opens on its first-carried line of business, in BOOK_LOBS order —
// the rule BookView's row click uses — so a DRP-only dairy does not land on an
// empty Spring tab. Read-only: it navigates, it fetches nothing.
function quickSwitcherCustomers(farmers) {
  const byKey = new Map();
  for (const f of farmers || []) {
    const key = sortNameKey(f.name);
    if (!key) continue;
    let g = byKey.get(key);
    if (!g) { g = { key, id: f.id, name: f.name, counties: new Set(), lobs: new Set(), cardIds: new Set() }; byKey.set(key, g); }
    g.cardIds.add(f.id);
    if (f.county) g.counties.add(normalizeCounty(f.county));
    for (const r of f.approvedYields || []) {
      const c = String(r.crop || "").toUpperCase();
      if (!c) continue;
      g.lobs.add(FALL_CROPS.includes(c) ? "fall" : "spring");
    }
    if ((f.drpPolicies || []).length) g.lobs.add("drp");
    if ((f.lrpPolicies || []).length) g.lobs.add("lrp");
    if ((f.prfPolicies || []).length) g.lobs.add("prf");
  }
  return [...byKey.values()].map(g => ({
    ...g,
    section: (BOOK_LOBS.find(l => g.lobs.has(l.key)) || {}).key || "spring",
    place: [...g.counties].map(titleCaseCounty).join(", "),
  }));
}

// Card ids of the customers opened most recently this session, newest first.
// Filled by Dashboard whenever a customer card opens; memory only.
const _recentCustomerIds = [];

function QuickSwitcher({ farmers, pages, onOpenCustomer, onOpenPage, onClose }) {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);
  const customers = useMemo(() => quickSwitcherCustomers(farmers), [farmers]);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    // Word-start matches rank above mid-word ones: "kast" finds KAST FARMS before
    // anything that merely contains "kast".
    const score = (text) => {
      const t = String(text || "").toLowerCase();
      if (!s) return 1;
      if (t.startsWith(s)) return 3;
      if (t.split(/[\s,&.()-]+/).some(w => w.startsWith(s))) return 2;
      return t.includes(s) ? 1 : 0;
    };
    const pg = pages.map(p => ({ kind: "page", ...p, sc: score(p.label) })).filter(r => r.sc > 0);
    const cu = customers.map(c => ({ kind: "customer", ...c, sc: Math.max(score(c.name), s ? score(c.place) * 0.5 : 0) }))
      .filter(r => r.sc > 0);
    if (!s) {
      // Empty box: the growers you opened most recently come first — the usual
      // reason to open the switcher is to get back to one of them.
      const recent = [];
      for (const id of _recentCustomerIds) {
        const c = customers.find(x => x.cardIds.has(id));
        if (c && !recent.some(r => r.key === c.key)) recent.push({ kind: "recent", ...c, sc: 1 });
      }
      const rest = cu.filter(c => !recent.some(r => r.key === c.key)).sort((a, b) => a.name.localeCompare(b.name));
      return [...recent, ...pg.slice(0, 6), ...rest.slice(0, recent.length ? 3 : 6)];
    }
    pg.sort((a, b) => b.sc - a.sc);
    cu.sort((a, b) => b.sc - a.sc || a.name.localeCompare(b.name));
    return [...pg.slice(0, 5), ...cu.slice(0, 10)];
  }, [q, pages, customers]);
  useEffect(() => { setHi(0); }, [q]);
  const choose = (r) => {
    if (!r) return;
    if (r.kind === "page") onOpenPage(r.key); else onOpenCustomer(r.id, r.section);
    onClose();
  };
  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setHi(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(results[hi]); }
  };
  let lastKind = null;
  return (
    <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(10,20,10,0.45)",
      display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "12vh" }}>
      <div onMouseDown={e => e.stopPropagation()} role="dialog" aria-label="Quick switcher"
        style={{ width: 560, maxWidth: "92vw", background: T.surface, borderRadius: T.r.lg, boxShadow: "0 20px 60px rgba(0,0,0,0.35)", overflow: "hidden", fontFamily: T.sans }}>
        <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey}
          placeholder="Jump to a customer or page…"
          style={{ width: "100%", boxSizing: "border-box", border: "none", borderBottom: `1px solid ${T.line}`, padding: "16px 18px", fontSize: 16, outline: "none", fontFamily: "inherit", color: T.ink }} />
        <div style={{ maxHeight: "55vh", overflowY: "auto", padding: "6px 0" }}>
          {results.map((r, i) => {
            const head = r.kind !== lastKind ? (r.kind === "page" ? "Pages" : r.kind === "recent" ? "Recent" : "Customers") : null;
            lastKind = r.kind;
            return (
              <React.Fragment key={r.kind + (r.key || r.id)}>
                {head && <div style={{ ...uiLabel, padding: "8px 18px 4px" }}>{head}</div>}
                <div onMouseEnter={() => setHi(i)} onClick={() => choose(r)}
                  style={{ padding: "8px 18px", cursor: "pointer", display: "flex", alignItems: "baseline", gap: 10,
                           background: i === hi ? "#eef6ef" : "transparent" }}>
                  <span style={{ width: 20, textAlign: "center" }}>{r.kind === "page" ? r.icon : r.kind === "recent" ? "🕘" : "📇"}</span>
                  <span style={{ fontWeight: 700, color: T.ink, fontSize: 14 }}>{r.kind === "page" ? r.label : r.name}</span>
                  {r.kind !== "page" && r.place && <span style={{ fontSize: 12, color: T.muted }}>{r.place}</span>}
                </div>
              </React.Fragment>
            );
          })}
          {!results.length && <div style={{ padding: "18px", color: T.muted, fontSize: 13 }}>Nothing matches “{q}”.</div>}
        </div>
        <div style={{ borderTop: `1px solid ${T.line}`, padding: "7px 18px", fontSize: 11, color: T.faint, display: "flex", gap: 14 }}>
          <span>↑↓ to move</span><span>Enter to open</span><span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}

// The list REMEMBERS where you were. Opening a grower and pressing Back used to
// remount this view at the top with every filter cleared, so with 282 growers
// the agent had to find their place again each time. Search, filters, sort and
// scroll position are kept for the life of the page — in memory, never browser
// storage, so a reload (including entering or leaving Preview-as) starts clean.
// Keyed by lockedLob so a locked single-coverage list keeps its own state.
const _bookViewMemory = {};

function BookView({ farmers = [], prices = {}, onSelect, isMobile = false, lockedLob = null }) {
  const memKey = lockedLob || "all";
  const mem = _bookViewMemory[memKey] || {};
  const [query, setQuery]       = useState(mem.query || "");
  const [lobFilter, setLobFilter] = useState(() => new Set(mem.lobFilter || (lockedLob ? [lockedLob] : [])));
  const [selState, setSelState]   = useState(() => new Set(mem.selState || []));
  const [selCounty, setSelCounty] = useState(() => new Set(mem.selCounty || []));
  const [selAgent, setSelAgent]   = useState(() => new Set(mem.selAgent || []));
  const [selCrop, setSelCrop]     = useState(() => new Set(mem.selCrop || []));
  const [sortMode, setSortMode]   = useState(mem.sortMode || "name");   // name | value | acres
  useEffect(() => {
    _bookViewMemory[memKey] = { ...(_bookViewMemory[memKey] || {}), query, sortMode,
      lobFilter: [...lobFilter], selState: [...selState], selCounty: [...selCounty], selAgent: [...selAgent], selCrop: [...selCrop] };
  }, [memKey, query, sortMode, lobFilter, selState, selCounty, selAgent, selCrop]);
  // The page scrolls the WINDOW on desktop and an inner container on a phone, so
  // find whichever ancestor actually scrolls rather than assuming one.
  const rootRef = useRef(null);
  const scroller = () => {
    for (let el = rootRef.current && rootRef.current.parentElement; el; el = el.parentElement) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) return el;
    }
    return document.scrollingElement || document.documentElement;
  };
  const rememberScroll = () => { try { _bookViewMemory[memKey] = { ..._bookViewMemory[memKey], scroll: scroller().scrollTop }; } catch { /* nothing to remember */ } };
  const restoredRef = useRef(false);

  const growers = useMemo(() => {
    const norm = sortNameKey;   // order-insensitive so "RICE, CRAIG" == "CRAIG RICE"
    const byName = {};
    (farmers || []).forEach(f => {
      const key = norm(f.name);
      if (!key) return;
      if (!byName[key]) {
        byName[key] = {
          key, id: f.id, name: f.name, state: f.state, agent: f._agentOverride || f.agent,
          // normalizeCounty upper-cases and strips the practice suffix. Without it
          // the same county arrived twice in different cases and rendered as
          // "ORLEANS, Orleans" on the row.
          counties: new Set(f.county ? [normalizeCounty(f.county)] : []),
          approvedYields: [...(f.approvedYields || [])],
          ppEligibility:  [...(f.ppEligibility || [])],
          scoEco:         [...(f.scoEcoEndorsements || [])],
          drpPolicies:    [...(f.drpPolicies || [])],
          lrpPolicies:    [...(f.lrpPolicies || [])],
          prfPolicies:    [...(f.prfPolicies || [])],
          // ⚠ CLAIMS ARE COLLECTED PER GROWER so the chip can filter on them. They
          // carry their own crop_year and are SCOPED at filter time, never here —
          // an unscoped 'no claim' means 'never had one, ever' and drops exactly the
          // farms an agent is calling about.
          claims: [...(f.claims || [])],
          cards: [f],
        };
      } else {
        const g = byName[key];
        if (f.county) g.counties.add(normalizeCounty(f.county));
        (f.approvedYields || []).forEach(x => g.approvedYields.push(x));
        (f.ppEligibility  || []).forEach(x => g.ppEligibility.push(x));
        (f.scoEcoEndorsements || []).forEach(x => g.scoEco.push(x));
        (f.drpPolicies || []).forEach(x => g.drpPolicies.push(x));
        (f.lrpPolicies || []).forEach(x => g.lrpPolicies.push(x));
        (f.prfPolicies || []).forEach(x => g.prfPolicies.push(x));
        (f.claims || []).forEach(x => g.claims.push(x));
        g.cards.push(f);
        if (!g.state && f.state) g.state = f.state;
      }
    });

    // Dedup coverage across a grower's cards by identity WITHOUT crop year, keeping
    // the row with the larger liability. Mirrors PortalHome and the old worklist.
    const dedupBy = (arr, keyFn) => {
      const seen = new Map();
      (arr || []).forEach(r => {
        const k = keyFn(r); const cur = seen.get(k);
        if (!cur || (Number(r.liability) || 0) > (Number(cur.liability) || 0)) seen.set(k, r);
      });
      return [...seen.values()];
    };
    const covKey = r => `${(r.county||"").toUpperCase()}|${(r.crop||"").toUpperCase()}|${r.plan||""}|${r.type||""}|${r.practice||""}`;

    return Object.values(byName).map(g => {
      const ay     = dedupBy(g.approvedYields, covKey);
      const scoEco = dedupBy(g.scoEco, covKey);
      const lobs = new Set();
      let premium = 0;
      // ⚠ A CROP COUNTS WHEN A COVERAGE ROW CARRIES ACRES OR LIABILITY. The chip
      // used to be added for any row that named a crop, so a policy line the
      // grower reported nothing on put him in that crop's list — Robert Orr in
      // Corn while all he carries is 5.1 acres of grapes.
      const cropsSet = new Set();          // carried
      const cropsSeen = new Set();         // named anywhere, carried or not
      // Per-coverage liability/acres so the columns can scope to the active
      // coverage filter (only crops carry acres). Total = sum across all lobs.
      const _libBy = { spring: 0, fall: 0, drp: 0, lrp: 0, prf: 0 };
      const _acBy  = { spring: 0, fall: 0, prf: 0 };   // PRF is acreage-based; DRP/LRP are not

      ay.forEach(r => {
        const c = (r.crop || "").toUpperCase();
        const lob = FALL_CROPS.includes(c) ? "fall" : "spring";
        _libBy[lob] += Number(r.liability) || 0;
        _acBy[lob]  += Number(r.acres) || 0;
        premium     += Number(r.totalPremium) || 0;
        // Measured over the book: 510 crop-chips carry both acres and liability,
        // 3 carry acres with no liability, 0 carry liability alone, 125 carry
        // neither. The OR is there for those 3.
        const carried = (Number(r.acres) || 0) > 0 || (Number(r.liability) || 0) > 0;
        if (c) { cropsSeen.add(c); if (carried) cropsSet.add(c); }
        lobs.add(lob);
      });
      scoEco.forEach(r => { const c = (r.crop || "").toUpperCase(); _libBy[FALL_CROPS.includes(c) ? "fall" : "spring"] += Number(r.liability) || 0; });
      (g.ppEligibility || []).forEach(p => {
        const c = (p.crop || "").toUpperCase();
        // ⚠ PP ELIGIBILITY IS NOT COVERAGE. It is a hand-uploaded worksheet of the
        // MAXIMUM prevent-plant acres a crop could claim — no sync writes it, and
        // CLAUDE.md records that it is typed in every crop year — so it says what
        // could be prevented, not what is insured now. Orr carries 466.9 acres of
        // CORN eligibility against a corn coverage with no acres and no liability;
        // leaving this adding to cropsSet would have made the whole fix a no-op on
        // the very farm Mike named. It still counts toward the coverage LOB and is
        // still remembered below as a crop seen.
        if (c) { cropsSeen.add(c); if (FALL_CROPS.includes(c)) lobs.add("fall"); else lobs.add("spring"); }
      });

      const drpSeen = new Set(), lrpSeen = new Set(), prfSeen = new Set();
      g.drpPolicies.forEach(p => (p.endorsements || []).forEach(e => {
        const k = `${e.quarter||""}|${e.endNum??""}|${e.effectiveDate||""}|${e.classIII??""}`;
        if (drpSeen.has(k)) return; drpSeen.add(k);
        _libBy.drp += Number(e.liability ?? e.expRevGuar) || 0; lobs.add("drp");
      }));
      g.lrpPolicies.forEach(p => (p.endorsements || []).forEach(e => {
        const k = `${e.lineId??""}|${e.salesDate||e.sales_date||""}|${e.head??e.numberOfHead??""}`;
        if (lrpSeen.has(k)) return; lrpSeen.add(k);
        _libBy.lrp += Number(e.insured_value ?? e.insuredValue ?? e.liability) || 0; lobs.add("lrp");
      }));
      g.prfPolicies.forEach(p => {
        const k = p.policyNumber || p.id || `${p.county||""}|${p.totalLiability||""}`;
        if (prfSeen.has(k)) return; prfSeen.add(k);
        _libBy.prf += Number(p.totalLiability) || (p.intervals || []).reduce((s, iv) => s + (Number(iv.liability) || 0), 0);
        _acBy.prf  += Number(p.totalAcres) || 0;   // stored per-policy (can't naive-sum intervals)
        lobs.add("prf");
      });

      const liability = _libBy.spring + _libBy.fall + _libBy.drp + _libBy.lrp + _libBy.prf;
      const acres = _acBy.spring + _acBy.fall + _acBy.prf;
      // Named on a policy but carrying nothing. Kept so the page can SAY what it
      // set aside rather than quietly shortening a list.
      const cropsUnreported = [...cropsSeen].filter(c => !cropsSet.has(c)).sort();
      return { ...g, counties: [...g.counties], crops: [...cropsSet].sort(), cropsUnreported,
               lobs: [...lobs], liability, acres, premium, _libBy, _acBy };
    });
  }, [farmers]);

  // ⚠ THE TYPES COME FROM THE DATA, never a hardcoded list — the same rule the
  // Coverage Book Scan's option list follows. A stage the AIPs stop sending
  // disappears on its own, and one they start sending appears without an edit.
  // Measured CY2026: Harvest Loss, Prevent Plant, Replant, Notice Filed.

  const states = useMemo(
    () => [...new Set(growers.map(g => g.state).filter(Boolean))].sort(), [growers]);
  const agents = useMemo(
    () => AGENCY_AGENTS.filter(a => growers.some(g => canonAgent(g.agent) === a)), [growers]);
  const crops = useMemo(
    () => [...new Set(growers.flatMap(g => g.crops))].sort(), [growers]);
  // Counties are only offered once a state is picked — the whole-book county list
  // is 40+ chips, but scoped to the selected state(s) it's a short, useful drill-in.
  const counties = useMemo(
    () => selState.size
      ? [...new Set(growers.filter(g => selState.has(g.state)).flatMap(g => g.counties))].sort()
      : [], [growers, selState]);
  // Drop any selected county that isn't in the current state's list (e.g. state changed).
  useEffect(() => {
    setSelCounty(prev => { if (!prev.size) return prev; const ok = new Set(counties); const n = new Set([...prev].filter(c => ok.has(c))); return n.size === prev.size ? prev : n; });
  }, [counties]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    // The Liability/Acres columns scope to the selected coverage chips (no chip =
    // total across every coverage). Acres only exist for crops (spring/fall).
    const ALL_LOBS = ["spring", "fall", "drp", "lrp", "prf"];
    const scopeLibs = lobFilter.size ? ALL_LOBS.filter(l => lobFilter.has(l)) : ALL_LOBS;
    const scopeAcs  = scopeLibs.filter(l => l === "spring" || l === "fall" || l === "prf");
    let list = growers.filter(g => {
      if (lobFilter.size && !g.lobs.some(l => lobFilter.has(l))) return false;
      if (selState.size && !selState.has(g.state)) return false;
      if (selCounty.size && !g.counties.some(c => selCounty.has(c))) return false;
      if (selAgent.size && !selAgent.has(canonAgent(g.agent))) return false;
      if (selCrop.size && !g.crops.some(c => selCrop.has(c))) return false;
      // ⚠ CROP-YEAR SCOPED, and a farm with no coverage this year is in NEITHER
      // bucket — it cannot have opened a claim, so counting it as 'no claim' pads
      // the call list with farms that are not in the book this year.
      if (q) {
        const hay = `${g.name} ${g.counties.join(" ")} ${g.crops.join(" ")} ${g.agent || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).map(g => ({
      ...g,
      _dispLib: scopeLibs.reduce((s, l) => s + (g._libBy?.[l] || 0), 0),
      _dispAc:  scopeAcs.reduce((s, l) => s + (g._acBy?.[l] || 0), 0),
    }));
    if (sortMode === "name")       list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    else if (sortMode === "acres") list.sort((a, b) => b._dispAc - a._dispAc);
    else                           list.sort((a, b) => b._dispLib - a._dispLib);
    return list;
  // ⚠ claimFilter IS LOAD-BEARING IN THIS LIST AND WAS NOT DECLARED. Nothing
  // else here changes with it, so clicking the chip flipped its own `active`
  // styling and the memo never reran — the filter appeared to do nothing, and
  // then WORKED the moment any other filter was touched. That intermittency is
  // what made it read as a data problem: the claim rule had already been fixed
  // once (it read crop_year on rows carrying cropYear) and was still "broken".
  }, [growers, query, lobFilter, selState, selCounty, selAgent, selCrop, sortMode]);

  const totals = useMemo(() => ({
    growers: rows.length,
    policies: rows.reduce((s, g) => s + g.cards.length, 0),
    acres: rows.reduce((s, g) => s + g._dispAc, 0),
    liability: rows.reduce((s, g) => s + g._dispLib, 0),
  }), [rows]);

  const toggleSet = (setter) => (v) => setter(prev => {
    const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n;
  });

  // Place / crop line under the name. Every part is optional — a grower with no
  // county and no state used to render a bare "· NY" or a dangling separator.
  const placeLine = (g) => {
    const parts = [];
    if (g.counties.length) {
      const shown = g.counties.slice(0, 2).map(titleCaseCounty).join(", ");
      parts.push(g.counties.length > 2 ? `${shown} +${g.counties.length - 2}` : shown);
    }
    if (g.state) parts.push(g.state);
    if (g.cards.length > 1) parts.push(`${g.cards.length} policies`);
    return parts.join(" · ");
  };

  const HEAD = { ...uiLabel, fontSize: 10.5 };

  // Put the list back where it was, once, after the rows exist to scroll to.
  useEffect(() => {
    if (restoredRef.current || !rows.length) return;
    restoredRef.current = true;
    const y = (_bookViewMemory[memKey] || {}).scroll;
    if (y > 0) requestAnimationFrame(() => { try { scroller().scrollTop = y; } catch { /* ignore */ } });
  }, [rows.length]);

  return (
    <div ref={rootRef} style={{ maxWidth: 1150, margin: "0 auto" }}>
      <div style={{ marginBottom: T.sp.lg }}>
        <h2 style={{ margin: 0, color: T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>All Customers</h2>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 3 }}>
          Every grower in the book, with all of their coverage on one row.
        </div>
      </div>

      <div style={{ display: "flex", gap: T.sp.md, flexWrap: "wrap", marginBottom: T.sp.lg }}>
        <StatTile label="Growers"       value={String(totals.growers)}  sub="matching filters" isMobile={isMobile} />
        <StatTile label="Policies"      value={String(totals.policies)} sub="cards behind them" isMobile={isMobile} />
        <StatTile label="Insured Acres" value={Math.round(totals.acres).toLocaleString()} sub="spring + fall" isMobile={isMobile} />
        <StatTile label="Liability"     value={fmtD0(totals.liability)} sub="revenue protected" isMobile={isMobile} />
      </div>

      {/* Filters. Coverage and State are separate LABELLED rows — in the first
          build they were one undifferentiated run of chips, so the LOB filters and
          the state filters looked like the same control. */}
      <div style={{ ...uiCard(isMobile ? 12 : 14), marginBottom: T.sp.md, display: "flex", flexDirection: "column", gap: 10 }}>
        <input value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search grower, county, or crop…"
          style={{ width: "100%", padding: "9px 13px", borderRadius: T.r.md, border: `1px solid ${T.lineStrong}`,
                   fontSize: 13, fontFamily: "inherit", color: T.ink, boxSizing: "border-box" }} />

        {!lockedLob && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ ...HEAD, width: 62, flexShrink: 0 }}>Coverage</span>
            {BOOK_LOBS.map(l => (
              <Chip key={l.key} active={lobFilter.has(l.key)} tone={SECTION_META[l.key]}
                onClick={() => toggleSet(setLobFilter)(l.key)}>{l.label}</Chip>
            ))}
          </div>
        )}

        {states.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ ...HEAD, width: 62, flexShrink: 0 }}>State</span>
            {states.map(s => (
              <Chip key={s} active={selState.has(s)} onClick={() => toggleSet(setSelState)(s)}>{s}</Chip>
            ))}
          </div>
        )}

        {/* ⚠ A FILTER THAT QUIETLY SHORTENS A LIST IS HOW THIS DEFECT SURVIVED.
            A crop chip now means the grower CARRIES that crop (a coverage row with
            acres or liability). Growers who hold a policy for it and reported
            nothing are set aside — counted and named here, never dropped in
            silence. Measured 2026-09-08: 125 such crop-chips across 83 farms. */}
        {selCrop.size > 0 && (() => {
          const held = growers.filter(g => (g.cropsUnreported || []).some(c => selCrop.has(c))
                                        && !g.crops.some(c => selCrop.has(c)));
          if (!held.length) return null;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ ...HEAD, width: 62, flexShrink: 0 }} />
              <span style={{ fontSize: 11.5, color: "#8a6d3b" }}>
                {held.length} more grower{held.length === 1 ? "" : "s"} hold a policy for{" "}
                {[...selCrop].join(", ").toLowerCase()} with no acres or liability reported — not listed
                {held.length <= 4 ? ": " + held.map(g => g.name).join(", ") : ""}.
              </span>
            </div>
          );
        })()}

        {counties.length > 0 && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
            <span style={{ ...HEAD, width: 62, flexShrink: 0, marginTop: 6 }}>County</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
              {counties.map(c => (
                <Chip key={c} active={selCounty.has(c)} onClick={() => toggleSet(setSelCounty)(c)}>{titleCaseCounty(c)}</Chip>
              ))}
            </div>
          </div>
        )}

        {agents.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ ...HEAD, width: 62, flexShrink: 0 }}>Agent</span>
            {agents.map(a => (
              <Chip key={a} active={selAgent.has(a)} onClick={() => toggleSet(setSelAgent)(a)}>{a}</Chip>
            ))}
          </div>
        )}

        {crops.length > 1 && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
            <span style={{ ...HEAD, width: 62, flexShrink: 0, marginTop: 6 }}>Crop</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
              {crops.map(c => (
                <Chip key={c} active={selCrop.has(c)} onClick={() => toggleSet(setSelCrop)(c)}>{titleCaseCounty(c)}</Chip>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ ...HEAD, width: 62, flexShrink: 0 }}>Sort</span>
          {[["value", "Liability"], ["acres", "Acres"], ["name", "Name"]].map(([k, lab]) => (
            <Chip key={k} active={sortMode === k} onClick={() => setSortMode(k)}>{lab}</Chip>
          ))}
          {(lobFilter.size > (lockedLob ? 1 : 0) || selState.size > 0 || selCounty.size > 0 || selAgent.size > 0 || selCrop.size > 0 || query) && (
            <button onClick={() => { setLobFilter(new Set(lockedLob ? [lockedLob] : [])); setSelState(new Set()); setSelCounty(new Set()); setSelAgent(new Set()); setSelCrop(new Set()); setQuery(""); }}
              style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer",
                       color: T.green, fontSize: 11.5, fontWeight: 700, fontFamily: "inherit" }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Column headers — the numbers on the right were unlabelled, so $917,886
          next to 1,591 ac was ambiguous on first read. */}
      {!isMobile && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 18px 6px", }}>
          <span style={{ width: 12 }} />
          <span style={{ ...HEAD, flex: "1 1 200px" }}>Grower</span>
          <span style={{ ...HEAD, width: 250 }}>Coverage</span>
          <span style={{ ...HEAD, width: 110, textAlign: "right" }}>Liability</span>
          <span style={{ ...HEAD, width: 84, textAlign: "right" }}>Acres</span>
        </div>
      )}

      {/* Desktop: ONE card of divided rows, not 282 separate cards. Each grower
          used to be its own padded card with an 8px gap, which made this page
          ~22,700px tall; a ruled list carries the same columns in well under
          half the height. Mobile keeps separate cards — they are the tap target. */}
      <div style={isMobile ? { display: "flex", flexDirection: "column", gap: 8 }
                           : { ...uiCard(0), overflow: "hidden" }}>
        {rows.map((g, gi) => {
          const zero = !(g._dispLib > 0);
          // Clicking the row opens the card on its first-carried LOB; clicking a
          // specific coverage pill jumps straight to that LOB's tab.
          const defaultSection = (BOOK_LOBS.find(l => g.lobs.includes(l.key)) || {}).key || g.lobs[0];
          return (
            <div key={g.key} onClick={() => { rememberScroll(); onSelect && onSelect(g.id, defaultSection); }}
              style={isMobile ? { ...uiCard(13), cursor: "pointer" }
                              : { padding: "9px 18px", cursor: "pointer", borderTop: gi ? `1px solid ${T.line}` : "none" }}
              onMouseEnter={isMobile ? undefined : (ev => { ev.currentTarget.style.background = "#f6f9f6"; })}
              onMouseLeave={isMobile ? undefined : (ev => { ev.currentTarget.style.background = ""; })}
              title={`Open ${g.name}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                <span style={{ width: 12, flexShrink: 0 }} />

                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: T.ink, fontSize: 14, lineHeight: 1.3 }}>{g.name}</div>
                  {placeLine(g) && (
                    <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>{placeLine(g)}</div>
                  )}
                </div>

                {/* Each coverage pill is its own link — click it to open the card on
                    that coverage. Fixed-width slot so pills form a readable column. */}
                <div style={{ width: isMobile ? "100%" : 250, flexShrink: 0, display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {BOOK_LOBS.filter(l => g.lobs.includes(l.key)).map(l => (
                    <span key={l.key} onClick={(e) => { e.stopPropagation(); rememberScroll(); onSelect && onSelect(g.id, l.key); }}
                      title={`Open ${SECTION_META[l.key]?.label || l.label}`} style={{ cursor: "pointer", display: "inline-flex" }}>
                      <Chip tone={SECTION_META[l.key]}>{l.label}</Chip>
                    </span>
                  ))}
                </div>

                <div style={{ width: isMobile ? "auto" : 110, textAlign: "right", flexShrink: 0 }}>
                  <div style={{ ...uiNum, fontWeight: 800, fontSize: 13.5, color: zero ? T.faint : T.ink }}>
                    {zero ? "—" : fmtD0(g._dispLib)}
                  </div>
                  {isMobile && <div style={{ ...uiLabel, fontSize: 10 }}>Liability</div>}
                </div>
                <div style={{ width: isMobile ? "auto" : 84, textAlign: "right", flexShrink: 0 }}>
                  <div style={{ ...uiNum, fontSize: 12.5, color: g._dispAc > 0 ? T.body : T.faint }}>
                    {g._dispAc > 0 ? `${Math.round(g._dispAc).toLocaleString()} ac` : "—"}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {!rows.length && (
          <div style={{ ...(isMobile ? uiCard() : {}), textAlign: "center", padding: "40px 20px", color: T.muted, fontSize: 13 }}>
            No growers match these filters.
          </div>
        )}
      </div>
    </div>
  );
}

function PolicySectionList({ section, farmers, onSelect, isMobile, prices = {} }) {
  const [query, setQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortMode, setSortMode] = useState("az"); // "az" | "za"
  const [filterMissingAPH, setFilterMissingAPH] = useState(false);
  const [filterMissingAcres, setFilterMissingAcres] = useState(false);
  const [filterMissingPP, setFilterMissingPP] = useState(false);
  const [filterHasSilage, setFilterHasSilage] = useState(false);
  const [filterMissing578, setFilterMissing578] = useState(false);
  const [f578Rows, setF578Rows] = useState(null);   // farmer_578s for this season (null until loaded)
  const [filterState, setFilterState] = useState(() => new Set());
  const [filterCounty, setFilterCounty] = useState(() => new Set());
  const [filterCrop, setFilterCrop] = useState(() => new Set());
  const [filterAIP, setFilterAIP] = useState(() => new Set());
  const [filterAgent, setFilterAgent] = useState(() => new Set());
  const filterRef = useRef(null);
  const meta = SECTION_META[section];

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  // 578 docs for this season — to flag farmers missing acreage reports. Only
  // spring/fall need 578s (DRP/LRP/PRF don't), so skip the fetch otherwise.
  useEffect(() => {
    if (section !== "spring" && section !== "fall") { setF578Rows([]); return; }
    let alive = true;
    sbFetch("farmer_578s", { select: "farmer_id,county,is_nationwide", filters: [`season=eq.${section}`] })
      .then(r => { if (alive) setF578Rows(r || []); })
      .catch(() => { if (alive) setF578Rows([]); });
    return () => { alive = false; };
  }, [section]);

  const sectionFarmers = useMemo(() => {
    const filtered = farmers.filter(f => {
      // Hard exclusion: if a policy card's ONLY types are non-crop (prf/lrp/drp), never show on spring/fall
      const onlyNonCrop = f.policyTypes?.length > 0 && f.policyTypes.every(t => ["lrp","drp","prf"].includes(t));
      if (section === "fall") {
        if (onlyNonCrop) return false;
        return f.approvedYields?.some(a => meta.crops.includes(a.crop)) || f.ppEligibility?.some(p => meta.crops.includes((p.crop||"").toUpperCase()));
      }
      if (section === "spring") {
        if (onlyNonCrop) return false;
        
        const hasSpringYields = f.approvedYields?.some(a => !FALL_CROPS.includes(a.crop));
        const hasSpringPP = f.ppEligibility?.some(p => !FALL_CROPS.includes((p.crop||"").toUpperCase()));
        // Only show if there's actual spring data — no more empty cards
        return hasSpringYields || hasSpringPP;
      }
      if (section === "drp")    return f.drpPolicies?.length > 0;
      if (section === "lrp")    return f.lrpPolicies?.length > 0;
      if (section === "prf")    return f.prfPolicies?.length > 0 || f.policyTypes?.includes("prf");
      return false;
    });

    // DRP is cross-crop-year: merge all DRP endorsements for same farmer name into one virtual record
    if (section === "drp") {
      const byName = {};
      const normDrpName = (s) => (s||"").trim().toLowerCase().replace(/[^a-z0-9]/g,'');
      filtered.forEach(f => {
        const key = normDrpName(f.name);
        if (!key) return;
        if (!byName[key]) {
          byName[key] = { ...f, drpPolicies: [...(f.drpPolicies||[])].map(p=>({...p, endorsements:[...(p.endorsements||[])]})), _drpCropYears: [f.cropYear].filter(Boolean) };
        } else {
          // Merge DRP endorsements from additional crop year records
          const existing = byName[key];
          (f.drpPolicies||[]).forEach(pol => {
            const newEnds = (pol.endorsements||[]);
            if (existing.drpPolicies.length === 0) {
              existing.drpPolicies.push({ ...pol, endorsements: [...newEnds] });
            } else {
              // Union endorsements, dedup by quarter+endNum
              const endMap = {};
              (existing.drpPolicies[0].endorsements||[]).forEach(e => { endMap[`${e.quarter||''}|${e.endNum||''}`] = e; });
              newEnds.forEach(e => { endMap[`${e.quarter||''}|${e.endNum||''}`] = e; });
              existing.drpPolicies[0] = { ...existing.drpPolicies[0], endorsements: Object.values(endMap) };
            }
          });
          if (f.cropYear && !existing._drpCropYears.includes(f.cropYear)) {
            existing._drpCropYears.push(f.cropYear);
          }
        }
      });
      return Object.values(byName);
    }

    // Spring and Fall crops: merge same-name farmers across multiple policies/AIPs into one card
    // e.g. Rich Harvest Farms with both R&H and NAU crop policies should be one card
    if (section === "spring" || section === "fall") {
      const byName = {};
      const normName = (s) => (s||"").trim().toLowerCase().replace(/[^a-z0-9]/g,'');
      filtered.forEach(f => {
        const key = normName(f.name);
        if (!key) return;
        if (!byName[key]) {
          byName[key] = {
            ...f,
            approvedYields: [...(f.approvedYields||[])],
            ppEligibility: [...(f.ppEligibility||[])],
            scoEcoEndorsements: [...(f.scoEcoEndorsements||[])],
            claims: [...(f.claims||[])],
            allPolicyIds: [...(f.allPolicyIds||[])],
            _allPolicyNums: [f.policyNumber].filter(Boolean),
          };
        } else {
          const existing = byName[key];
          // Merge approvedYields — dedup by county+crop+type+practice
          (f.approvedYields||[]).forEach(ay => {
            const dupeKey = `${(ay.county||"").toUpperCase()}|${(ay.crop||"").toUpperCase()}|${(ay.type||"").toUpperCase()}|${(ay.practice||"").toUpperCase()}`;
            if (!existing.approvedYields.some(e =>
              `${(e.county||"").toUpperCase()}|${(e.crop||"").toUpperCase()}|${(e.type||"").toUpperCase()}|${(e.practice||"").toUpperCase()}` === dupeKey
            )) existing.approvedYields.push(ay);
          });
          // Merge PP eligibility
          (f.ppEligibility||[]).forEach(pp => {
            const dupeKey = `${(pp.county||"").toUpperCase()}|${(pp.crop||"").toUpperCase()}|${(pp.type||"").toUpperCase()}`;
            if (!existing.ppEligibility.some(e =>
              `${(e.county||"").toUpperCase()}|${(e.crop||"").toUpperCase()}|${(e.type||"").toUpperCase()}` === dupeKey
            )) existing.ppEligibility.push(pp);
          });
          // Merge SCO/ECO endorsements
          (f.scoEcoEndorsements||[]).forEach(s => existing.scoEcoEndorsements.push(s));
          // Merge claims/indemnities
          (f.claims||[]).forEach(c => existing.claims.push(c));
          // Track all policy IDs and numbers
          (f.allPolicyIds||[]).forEach(pid => { if (!existing.allPolicyIds.includes(pid)) existing.allPolicyIds.push(pid); });
          if (f.policyNumber && !existing._allPolicyNums.includes(f.policyNumber)) existing._allPolicyNums.push(f.policyNumber);
          // Keep the first farmer's metadata but note multiple AIPs
          if (f.aip && f.aip !== existing.aip) existing.aip = "Multiple";
          // Preserve has2025Prod — false if ANY card was false
          if (f.has2025Prod === false) existing.has2025Prod = false;
        }
      });
      // Update policyNumber display for merged farmers
      Object.values(byName).forEach(f => {
        if (f._allPolicyNums?.length > 1) {
          f.policyNumber = f._allPolicyNums.join(" · ");
        }
      });
      return Object.values(byName);
    }

    return filtered;
  }, [farmers, section]);

  // All counties present in this section for the county filter dropdown
  const allCounties = useMemo(() => {
    const set = new Set();
    sectionFarmers.forEach(f => {
      (f.approvedYields||[]).forEach(a => { if (a.county) set.add(normalizeCounty(a.county)); });
      (f.ppEligibility||[]).forEach(p => { if (p.county) set.add(normalizeCounty(p.county)); });
      if (f.county) set.add(normalizeCounty(f.county));
    });
    return [...set].sort();
  }, [sectionFarmers]);

  const allStates = useMemo(() => [...new Set(sectionFarmers.map(f => (f.state||"").toUpperCase().trim()).filter(Boolean))].sort(), [sectionFarmers]);
  const allCrops = useMemo(() => [...new Set([
    ...sectionFarmers.flatMap(f => (f.approvedYields||[]).map(a => (a.crop||"").toUpperCase()).filter(Boolean)),
    ...sectionFarmers.flatMap(f => (f.ppEligibility||[]).map(p => (p.crop||"").toUpperCase()).filter(Boolean)),
  ])].sort(), [sectionFarmers]);
  const allAIPs = useMemo(() => [...new Set(sectionFarmers.map(f => f.aip).filter(Boolean))].sort()
    .map(a => ({ value:a, label: a === "RAIN_AND_HAIL" ? "Rain & Hail" : a })), [sectionFarmers]);
  const allAgents = useMemo(() => [...new Set(sectionFarmers.map(f => (f.agent||"").trim()).filter(Boolean))].sort(), [sectionFarmers]);

  // Farmers (by list id) missing a 578 for this season: they have season policy
  // counties, no nationwide doc, and at least one county with no 578 on file —
  // same completeness rule as the per-farmer Farmer578Section.
  const missing578Ids = useMemo(() => {
    const ids = new Set();
    if (!f578Rows || (section !== "spring" && section !== "fall")) return ids;
    const byFid = {};
    f578Rows.forEach(d => {
      if (!d.farmer_id) return;
      const e = byFid[d.farmer_id] || (byFid[d.farmer_id] = { nationwide: false, counties: new Set() });
      if (d.is_nationwide) e.nationwide = true;
      if (d.county) e.counties.add(normalizeCounty(d.county));
    });
    sectionFarmers.forEach(f => {
      const pc = new Set();
      (f.approvedYields || []).forEach(ay => {
        const isFall = FALL_CROPS_SET.has((ay.crop || "").toUpperCase());
        if (((section === "fall") === isFall) && ay.county) pc.add(normalizeCounty(ay.county));
      });
      if (pc.size === 0) return;                       // no acreage to report → not "missing"
      const fids = f._allFarmerIds || [f.id];
      let nationwide = false; const received = new Set();
      fids.forEach(fid => { const e = byFid[fid]; if (e) { if (e.nationwide) nationwide = true; e.counties.forEach(c => received.add(c)); } });
      if (nationwide) return;
      if ([...pc].some(c => !received.has(c))) ids.add(f.id);
    });
    return ids;
  }, [f578Rows, sectionFarmers, section]);

  const activeFilterCount = [filterMissingAPH, filterMissingAcres, filterMissingPP, filterHasSilage, filterMissing578, filterState.size>0, filterCounty.size>0, filterCrop.size>0, filterAIP.size>0, filterAgent.size>0].filter(Boolean).length;

  const filtered = useMemo(() => {
    let list = sectionFarmers;

    // Text search
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(f =>
        f.name?.toLowerCase().includes(q) ||
        f.policyNumber?.toLowerCase().includes(q) ||
        f.agent?.toLowerCase().includes(q)
      );
    }

    // Missing 2025 Production: farmers whose spring crops don't all have 2025 production_history records
    if (filterMissingAPH) {
      list = list.filter(f => !f.has2025Prod);
    }

    // Missing acres:
    // Fall: check approvedYields.acres (acreage report) — fall wheat farmers don't use ppEligibility for acres
    // Spring: check ppEligibility.plantedAcres (entered on PP tab)
    if (filterMissingAcres) {
      list = list.filter(f => {
        if (section === "fall") {
          // Fall: flag only if EVERY APH yield row is missing acres (truly 0 acres farm-wide)
          const ayRows = (f.approvedYields||[]).filter(a => meta.crops.includes(a.crop));
          return ayRows.length > 0 && ayRows.every(a => !a.acres || parseFloat(a.acres) <= 0);
        } else {
          // Spring: flag only if EVERY complete PP+APH row is missing acres.
          // If even one county has acres entered, the farm is NOT flagged.
          const ppRows = (f.ppEligibility||[]).filter(p => !["WHEAT","BARLEY","TRITICALE","RYE","WINTER WHEAT"].includes((p.crop||"").toUpperCase()));
          if (!ppRows.length) return false;
          const completeRows = ppRows.filter(p =>
            (f.approvedYields||[]).some(a =>
              (a.county||"").toUpperCase().trim() === (p.county||"").toUpperCase().trim() &&
              (a.crop||"").toUpperCase() === (p.crop||"").toUpperCase() &&
              (a.type||'') === (p.type||'')
            )
          );
          if (!completeRows.length) return false;
          return completeRows.every(p => !p.plantedAcres || parseFloat(p.plantedAcres) <= 0);
        }
      });
    }

    // Missing PP eligibility: has crops but no PP rows
    if (filterMissingPP) {
      list = list.filter(f => {
        const ppRows = (f.ppEligibility||[]).filter(p =>
          section === "fall" ? meta.crops.includes((p.crop||"").toUpperCase()) : !["WHEAT","BARLEY","TRITICALE","RYE","WINTER WHEAT"].includes((p.crop||"").toUpperCase())
        );
        return ppRows.length === 0 || ppRows.every(p => !p.maxEligAcres || parseFloat(p.maxEligAcres) <= 0);
      });
    }

    // Has silage (GSS)
    if (filterHasSilage) {
      list = list.filter(f =>
        (f.approvedYields||[]).some(a => (a.type||"").toUpperCase() === "GSS") ||
        (f.ppEligibility||[]).some(p => (p.type||"").toUpperCase() === "GSS")
      );
    }

    // Missing 578 acreage report for this season
    if (filterMissing578) {
      list = list.filter(f => missing578Ids.has(f.id));
    }

    // By crop (multi)
    if (filterCrop.size) {
      list = list.filter(f =>
        (f.approvedYields||[]).some(a => filterCrop.has((a.crop||"").toUpperCase())) ||
        (f.ppEligibility||[]).some(p => filterCrop.has((p.crop||"").toUpperCase()))
      );
    }

    // By county (multi)
    if (filterCounty.size) {
      list = list.filter(f =>
        (f.approvedYields||[]).some(a => filterCounty.has(normalizeCounty(a.county))) ||
        (f.ppEligibility||[]).some(p => filterCounty.has(normalizeCounty(p.county))) ||
        filterCounty.has(normalizeCounty(f.county))
      );
    }

    // By state (multi)
    if (filterState.size) {
      list = list.filter(f => filterState.has((f.state||"").toUpperCase().trim()));
    }

    // By AIP (multi)
    if (filterAIP.size) {
      list = list.filter(f => filterAIP.has(f.aip));
    }

    // By agent (multi)
    if (filterAgent.size) {
      list = list.filter(f => filterAgent.has((f.agent||"").trim()));
    }

    // Sort — demo/presentation accounts (metadata.demo) always float to the top.
    list = [...list].sort((a, b) => {
      const da = a.metadata?.demo === true ? 0 : 1;
      const db = b.metadata?.demo === true ? 0 : 1;
      if (da !== db) return da - db;
      const na = (a.name||"").toLowerCase();
      const nb = (b.name||"").toLowerCase();
      return sortMode === "az" ? na.localeCompare(nb) : nb.localeCompare(na);
    });

    return list;
  }, [sectionFarmers, query, filterMissingAPH, filterMissingAcres, filterMissingPP, filterHasSilage, filterMissing578, missing578Ids, filterState, filterCounty, filterCrop, filterAIP, filterAgent, sortMode]);

  const resetFilters = () => {
    setFilterMissingAPH(false); setFilterMissingAcres(false);
    setFilterMissingPP(false); setFilterHasSilage(false); setFilterMissing578(false);
    setFilterState(new Set()); setFilterCounty(new Set()); setFilterCrop(new Set()); setFilterAIP(new Set()); setFilterAgent(new Set()); setSortMode("az");
  };

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
        <div style={{ fontSize:28 }}>{meta.icon}</div>
        <div>
          <h1 style={{ margin:0, fontSize:22, color:"#1a2e1a", fontFamily:"'Lora',serif" }}>{meta.label}</h1>
          {section !== "drp" && section !== "lrp" && (
            <div style={{ fontSize:12, color:"#60735f", marginTop:2 }}>{sectionFarmers.length} {sectionFarmers.length === 1 ? "policy" : "policies"} on file</div>
          )}
        </div>
      </div>

      {/* Search + Filter row */}
      {sectionFarmers.length > 2 && (
        <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder={`Search ${meta.label.toLowerCase()}...`}
            style={{ flex:1, padding:"10px 14px", border:"1px solid #dde5dd", borderRadius:8, fontSize:13, outline:"none", boxSizing:"border-box" }} />

          {/* Filter button + dropdown */}
          <div ref={filterRef} style={{ position:"relative", flexShrink:0 }}>
            <button onClick={() => setFilterOpen(o => !o)}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"10px 14px", border:`1px solid ${activeFilterCount > 0 ? "#4a7a2a" : "#dde5dd"}`, borderRadius:8, background: activeFilterCount > 0 ? "#eef6ec" : "#fff", cursor:"pointer", fontSize:13, fontWeight:700, color: activeFilterCount > 0 ? "#2d4a2d" : "#555", whiteSpace:"nowrap" }}>
              ⚙ Filter / Sort
              {activeFilterCount > 0 && (
                <span style={{ background:"#4a7a2a", color:"#fff", borderRadius:"50%", width:18, height:18, fontSize:10, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800 }}>{activeFilterCount}</span>
              )}
            </button>

            {filterOpen && (
              <div style={{ position:"absolute", right:0, top:"calc(100% + 6px)", background:"#fff", border:"1px solid #dde5dd", borderRadius:10, boxShadow:"0 4px 20px rgba(0,0,0,0.12)", zIndex:100, minWidth:260, padding:"14px 16px" }}>

                {/* Sort */}
                <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#677167", textTransform:"uppercase", marginBottom:8 }}>Sort</div>
                <div style={{ display:"flex", gap:6, marginBottom:14 }}>
                  {[["az","A → Z"],["za","Z → A"]].map(([val, label]) => (
                    <button key={val} onClick={() => setSortMode(val)}
                      style={{ flex:1, padding:"7px 0", borderRadius:6, border:`1px solid ${sortMode===val ? "#4a7a2a" : "#dde5dd"}`, background: sortMode===val ? "#eef6ec" : "#fafafa", color: sortMode===val ? "#2d4a2d" : "#555", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Filters */}
                <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#677167", textTransform:"uppercase", marginBottom:8 }}>Filter</div>
                {(section === "spring" || section === "fall") ? [
                  [filterMissingAPH,    setFilterMissingAPH,    `Missing ${CURRENT_CROP_YEAR - 1} production`],
                  [filterMissingAcres,  setFilterMissingAcres,  "Missing planted acres"],
                  [filterMissingPP,     setFilterMissingPP,     "Missing PP eligibility"],
                  [filterHasSilage,     setFilterHasSilage,     "Has silage (GSS)"],
                  [filterMissing578,    setFilterMissing578,    `Missing 578${f578Rows ? ` (${missing578Ids.size})` : ""}`],
                ].map(([val, setter, label]) => (
                  <label key={label} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid #f0f4f0", cursor:"pointer", fontSize:13, color:"#1a2e1a" }}>
                    <input type="checkbox" checked={val} onChange={e => setter(e.target.checked)}
                      style={{ width:16, height:16, accentColor:"#4a7a2a", cursor:"pointer" }} />
                    {label}
                  </label>
                )) : (
                  <div style={{ fontSize:12, color:"#999", padding:"6px 0", fontStyle:"italic" }}>
                    Use search to find policies
                  </div>
                )}

                {/* State / County / Crop / AIP / Agent — all multi-select */}
                {allStates.length > 1 && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#677167", textTransform:"uppercase", marginBottom:6 }}>State</div>
                    <FieldMultiSelect label="states" options={allStates} selected={filterState} onChange={setFilterState} />
                  </div>
                )}
                {allCounties.length > 1 && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#677167", textTransform:"uppercase", marginBottom:6 }}>County</div>
                    <FieldMultiSelect label="counties" options={allCounties} selected={filterCounty} onChange={setFilterCounty} />
                  </div>
                )}
                {(section === "spring" || section === "fall") && allCrops.length > 1 && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#677167", textTransform:"uppercase", marginBottom:6 }}>Crop</div>
                    <FieldMultiSelect label="crops" options={allCrops} selected={filterCrop} onChange={setFilterCrop} />
                  </div>
                )}
                {(section === "spring" || section === "fall") && allAIPs.length > 1 && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#677167", textTransform:"uppercase", marginBottom:6 }}>AIP</div>
                    <FieldMultiSelect label="AIPs" options={allAIPs} selected={filterAIP} onChange={setFilterAIP} />
                  </div>
                )}
                {allAgents.length > 1 && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#677167", textTransform:"uppercase", marginBottom:6 }}>Agent</div>
                    <FieldMultiSelect label="agents" options={allAgents} selected={filterAgent} onChange={setFilterAgent} />
                  </div>
                )}

                {/* Reset */}
                {(activeFilterCount > 0 || sortMode !== "az") && (
                  <button onClick={resetFilters}
                    style={{ marginTop:12, width:"100%", padding:"8px 0", borderRadius:7, border:"1px solid #e0d0d0", background:"#fafafa", color:"#c0392b", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    Reset all
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active filter summary */}
      {activeFilterCount > 0 && (
        <div style={{ fontSize:12, color:"#4a7a2a", marginBottom:12, fontWeight:600 }}>
          Showing {filtered.length} of {sectionFarmers.length} policies
          {filterState.size > 0 && ` · ${[...filterState].join(", ")}`}
          {filterCounty.size > 0 && ` · ${[...filterCounty].join(", ")} Co.`}
          {filterCrop.size > 0 && ` · ${[...filterCrop].join(", ")}`}
          {filterAIP.size > 0 && ` · ${[...filterAIP].map(a => a === "RAIN_AND_HAIL" ? "R&H" : a).join(", ")}`}
          {filterAgent.size > 0 && ` · ${[...filterAgent].join(", ")}`}
          {filterMissingAPH && " · Missing 2025 production"}
          {filterMissingAcres && " · Missing acres"}
          {filterMissingPP && " · Missing PP"}
          {filterHasSilage && " · Has silage"}
          {filterMissing578 && " · Missing 578"}
        </div>
      )}

      {sectionFarmers.length === 0 && (
        <Card style={{ textAlign:"center", padding:"48px 24px" }}>
          <div style={{ fontSize:36, marginBottom:12 }}>{meta.icon}</div>
          <div style={{ fontSize:15, fontWeight:800, color:"#1a2e1a", marginBottom:8 }}>No {meta.label} policies yet</div>
          <div style={{ fontSize:13, color:"#60735f" }}>Import PDFs using the ⬆️ Import tab to add policies.</div>
        </Card>
      )}
      {filtered.length === 0 && sectionFarmers.length > 0 && (
        <Card style={{ textAlign:"center", padding:"32px 24px" }}>
          <div style={{ fontSize:13, color:"#60735f" }}>No policies match the current filters.</div>
        </Card>
      )}

      <div style={{ display:"grid", gap:10 }}>
        {filtered.map(f => {
          
          const cropList = section === "fall"
            ? [...new Set([
                ...(f.approvedYields?.filter(a => meta.crops.includes(a.crop)).map(a => a.crop) || []),
                ...(f.ppEligibility?.filter(p => meta.crops.includes((p.crop||"").toUpperCase())).map(p => p.crop.toUpperCase()) || [])
              ])]
            : section === "spring"
            ? [...new Set([...(f.ppEligibility||[]).filter(p => !FALL_CROPS.includes((p.crop||"").toUpperCase())), ...(f.approvedYields?.filter(a => !FALL_CROPS.includes((a.crop||"").toUpperCase()))||[])].map(a => (a.crop||"").toUpperCase()))]
            : [];
          const ppCount  = section === "spring"
            ? (f.ppEligibility?.filter(p => !FALL_CROPS.includes((p.crop||"").toUpperCase())).length || 0)
            : (f.ppEligibility?.length || 0);
          const covCount = section === "spring"
            ? (f.approvedYields?.filter(a => !FALL_CROPS.includes((a.crop||"").toUpperCase())).length || 0)
            : (f.approvedYields?.filter(a => meta.crops.includes(a.crop)).length || 0);
          const drpCount = f.drpPolicies?.reduce((s,p) => s + (p.endorsements?.length||0), 0) || 0;
          const lrpCount = f.lrpPolicies?.reduce((s,p) => s + (p.endorsements?.length||0), 0) || 0;
          return (
            <Card key={f.policyId || f.id} style={{ padding: isMobile ? "14px 16px" : "18px 24px" }}>
              <div style={{ display:"flex", alignItems: isMobile?"flex-start":"center", justifyContent:"space-between", gap:12, flexDirection: isMobile?"column":"row" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize: isMobile?16:17, fontWeight:800, color:"#1a2e1a", fontFamily:"'Lora',serif", marginBottom:4 }}>{f.name}</div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", fontSize:12, color:"#60735f" }}>
                    <span>{f.policyNumber || "—"}</span>
                    {f.agent && <><span>·</span><span>Agent: {f.agent}</span></>}
                    {f.county && <><span>·</span><span>{f.county} Co.</span></>}
                  </div>
                  <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
                    {section === "drp" ? (() => {
                      // For DRP, show the year range across all merged policies (cross-crop-year)
                      const allYears = f._drpCropYears || [f.cropYear];
                      const yMin = Math.min(...allYears.filter(Boolean)), yMax = Math.max(...allYears.filter(Boolean));
                      const yearLabel = (!yMin || yMin === yMax) ? String(yMax||"") : `${yMin} – ${yMax}`;
                      return <span style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#e8f0e8", border:"1px solid #b8d0b8", color:"#2d4a2d", fontSize:11, fontWeight:800, padding:"3px 10px", borderRadius:20, letterSpacing:"0.06em" }}>🥛 {yearLabel}</span>;
                    })() : <CropYearBadge year={f.cropYear} />}
                    {cropList.map(crop => (
                      <span key={crop} style={{ fontSize:10, color:CROP_COLOR[crop]||"#666", background:CROP_BG[crop]||"#f4f8f4", border:`1px solid ${CROP_COLOR[crop]||"#ccc"}44`, padding:"2px 7px", borderRadius:3, fontWeight:700 }}>{crop}</span>
                    ))}
                    {section==="drp" && drpCount > 0 && <span style={{ fontSize:10, color:"#1a4a7a", background:"#e8f0fa", border:"1px solid #4a70aa44", padding:"2px 7px", borderRadius:3, fontWeight:700 }}>{drpCount} endorsements</span>}
                    {section==="lrp" && lrpCount > 0 && <span style={{ fontSize:10, color:"#7a3b10", background:"#fdf1e8", border:"1px solid #c8603044", padding:"2px 7px", borderRadius:3, fontWeight:700 }}>{lrpCount} endorsements</span>}
                    {section==="spring" && ppCount > 0 && <span style={{ fontSize:10, color:"#2d4a2d", background:"#eef6ec", border:"1px solid #4a7a2a44", padding:"2px 7px", borderRadius:3, fontWeight:700 }}>PP: {ppCount} crops</span>}
                    {section==="fall" && covCount > 0 && <span style={{ fontSize:10, color:"#6b4f00", background:"#f4f0e8", border:"1px solid #c8a84044", padding:"2px 7px", borderRadius:3, fontWeight:700 }}>SOI: {covCount} rows</span>}
                    {section==="fall" && (() => { const fallPP = f.ppEligibility?.filter(p => meta.crops.includes((p.crop||"").toUpperCase())).length || 0; return fallPP > 0 ? <span style={{ fontSize:10, color:"#6b4f00", background:"#f4f0e8", border:"1px solid #c8a84044", padding:"2px 7px", borderRadius:3, fontWeight:700 }}>PP: {fallPP} crops</span> : null; })()}
                  </div>
                  {/* Missing 2025 production detail — only shown when filter is active */}
                  {filterMissingAPH && f.missing2025Crops?.length > 0 && (
                    <div style={{ marginTop:8, fontSize:11, color:"#c0392b", fontWeight:600 }}>
                      ⚠️ Missing {CURRENT_CROP_YEAR - 1} production: {f.missing2025Crops.join(", ")}
                    </div>
                  )}
                  {/* ── SOI summary stats — total bu guarantee + acres ── */}
                  {section==="fall" && covCount > 0 && (() => {
                    const baseRows = (f.approvedYields||[]).filter(a =>
                      meta.crops.includes(a.crop) && a.plan && !a.plan.includes('ECO') && !a.plan.includes('SCO')
                    );
                    const totalAcres = baseRows.reduce((s,r) => s + (r.acres ? Number(r.acres) : 0), 0);
                    const totalBu    = baseRows.reduce((s,r) => {
                      // M13-direct: liability ÷ price (= SOI Marketable Guarantee); APH×cov×acres fallback only when no liability
                      const { price, unit } = getCropPriceAndUnit(r, prices, f.cropYear || CURRENT_CROP_YEAR);
                      // Divide by the ELECTED price (price x price-election percent), not the price.
                      // A CAT row elects 55%, so liability / price alone reads ~45% short.
                      const effPrice = electedPrice(price, unit, r);
                      // ⚠ Only a price map of THIS ROW'S crop year may divide its liability. Once
                      // CY2027 wheat liability is reported, the collapsed row is CY2027 while this
                      // list's map is the book year's — a CY2027 liability over a CY2026 price.
                      const _yearOk = r.cropYear == null || prices?._cropYear == null || Number(r.cropYear) === Number(prices._cropYear);
                      if (_yearOk && r.liability != null && effPrice > 0) return s + Number(r.liability) / effPrice;
                      if (!r.acres || !r.approvedYield || !r.coverageLevel) return s;
                      return s + (r.approvedYield * r.coverageLevel * Number(r.acres));
                    }, 0);
                    if (totalAcres === 0 && totalBu === 0) return null;
                    const counties = [...new Set(baseRows.map(r => r.county).filter(Boolean))];
                    return (
                      <div style={{ display:"flex", gap:16, marginTop:8, flexWrap:"wrap" }}>
                        {totalBu > 0 && (
                          <div style={{ display:"flex", alignItems:"center", gap:6, background:"#2a1e00", borderRadius:6, padding:"5px 12px" }}>
                            <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#c8a840", textTransform:"uppercase" }}>Bu. Guar.</span>
                            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:13, color:"#fff" }}>{Math.round(totalBu).toLocaleString()} bu</span>
                            {counties.length > 1 && <span style={{ fontSize:10, color:"#9a7a40" }}>{counties.length} counties</span>}
                          </div>
                        )}
                        {totalAcres > 0 && (
                          <div style={{ display:"flex", alignItems:"center", gap:6, background:"#f4f0e8", border:"1px solid #c8a84055", borderRadius:6, padding:"5px 12px" }}>
                            <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#9a7a40", textTransform:"uppercase" }}>Acres</span>
                            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:13, color:"#3a2800" }}>{totalAcres.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* ── Spring summary stats — per-crop bushel guarantee + total acres ──
                       Unlike fall (all WHEAT → one figure), spring is multi-crop and corn bu
                       ≠ soybean bu, so the guarantee is shown per crop, never summed across
                       crops. Acres ARE summable (total insured spring acreage). Only base
                       (non-SCO/ECO) rows count; ton-unit rows (silage / processing beans)
                       don't carry a bushel guarantee. */}
                  {section==="spring" && covCount > 0 && (() => {
                    const baseRows = (f.approvedYields||[]).filter(a =>
                      !FALL_CROPS.includes((a.crop||"").toUpperCase()) && a.plan && !a.plan.includes('ECO') && !a.plan.includes('SCO')
                    );
                    const totalAcres = baseRows.reduce((s,r) => s + (r.acres ? Number(r.acres) : 0), 0);
                    // Per-crop production guarantee: liability ÷ price, APH×cov×acres fallback.
                    // Only crops whose production unit is reliably known get a guarantee badge —
                    // getCropPriceAndUnit defaults every unmodeled crop to "bu", which is wrong
                    // for specialty crops (grapes/apples/peaches/onions are tons, etc.). Those show
                    // acres only rather than a mislabeled bushel figure.
                    const buByCrop = {};
                    baseRows.forEach(r => {
                      const crop = (r.crop||"").toUpperCase();
                      if (!GUAR_UNIT_CROPS.has(crop)) return;
                      const { price, unit } = getCropPriceAndUnit(r, prices, f.cropYear || CURRENT_CROP_YEAR);
                      if (unit === "ton") return; // corn silage — no bushel guarantee
                      // Divide by the ELECTED price (price x price-election percent), not the
                      // price. A CAT row elects 55%, so liability / price alone reads ~45% short.
                      const effPrice = electedPrice(price, unit, r);
                      let bu = null;
                      if (r.liability != null && effPrice > 0) bu = Number(r.liability) / effPrice;
                      else if (r.acres && r.approvedYield && r.coverageLevel) bu = r.approvedYield * r.coverageLevel * Number(r.acres);
                      if (!(bu > 0)) return;
                      if (!buByCrop[crop]) buByCrop[crop] = { bu: 0, unit };
                      buByCrop[crop].bu += bu;
                    });
                    const buEntries = Object.entries(buByCrop);
                    if (totalAcres === 0 && buEntries.length === 0) return null;
                    return (
                      <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
                        {buEntries.map(([crop, v]) => (
                          <div key={crop} style={{ display:"flex", alignItems:"center", gap:6, background:"#12240f", borderRadius:6, padding:"5px 12px" }}>
                            <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:CROP_COLOR[crop]||"#8fbf6a", textTransform:"uppercase" }}>{crop} Guar.</span>
                            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:13, color:"#fff" }}>{Math.round(v.bu).toLocaleString()} {v.unit}</span>
                          </div>
                        ))}
                        {totalAcres > 0 && (
                          <div style={{ display:"flex", alignItems:"center", gap:6, background:"#eef6ec", border:"1px solid #4a7a2a55", borderRadius:6, padding:"5px 12px" }}>
                            <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#4a7a2a", textTransform:"uppercase" }}>Acres</span>
                            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, fontSize:13, color:"#1f3d17" }}>{totalAcres.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <button onClick={() => onSelect(f.policyId || f.id)}
                  style={{ background:"#2d4a2d", color:"#fff", padding: isMobile?"10px 0":"8px 20px", borderRadius:7, border:"none", fontSize:12, fontWeight:800, letterSpacing:"0.06em", cursor:"pointer", width: isMobile?"100%":"auto", flexShrink:0 }}>
                  OPEN →
                </button>
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && sectionFarmers.length > 0 && (
          <div style={{ color:"#60735f", textAlign:"center", padding:"24px 0", fontSize:13 }}>No results for "{query}"</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRICE HISTORY — one crop at a time.
//
// This replaced a crosstab of 11 crops × 2 columns: 22 columns behind a
// horizontal scrollbar, where nine of the eleven crops were "—" in every prior
// year and every empty harvest cell read "TBD" — which says "coming soon" about
// years that closed three seasons ago. Nothing about that shape answered the
// question you open it with, which is about ONE crop: what did the guarantee do
// over the years, and did harvest ever lift it?
//
// Projected and harvest are the same measure at two points in the season, so
// they take ONE hue in two shades (an ordinal ramp), not two categorical
// colours — the same PA_PRIOR/PA_NOW pair Premium Analytics uses, already
// validated against this card surface. The light shade sits at 2.46:1, under
// the 3:1 line, which obliges visible labels: every bar carries its own figure
// and the table below is the full text view.
// ─────────────────────────────────────────────────────────────────────────────
function RmaPriceHistory({ crops, years, cropUnit, isMobile }) {
  // Only offer a crop that actually has a price in some year — the old grid
  // spent nine of its columns on crops with nothing in them.
  const withData = crops.filter(c => years.some(y =>
    resolveRmaPrice(c, y, "projected").price != null || resolveRmaPrice(c, y, "harvest").price != null));
  const [crop, setCrop] = useState(withData[0] || crops[0]);
  const sel = withData.includes(crop) ? crop : (withData[0] || crops[0]);
  const unit = cropUnit(sel);

  const rows = years.slice().sort((a, b) => a - b).map(y => {
    const p = resolveRmaPrice(sel, y, "projected");
    const h = resolveRmaPrice(sel, y, "harvest");
    const eff = p.price == null ? h.price : (h.price == null ? p.price : Math.max(p.price, h.price));
    return { year: y, proj: p, harv: h, eff, harWins: h.price != null && p.price != null && h.price > p.price };
  }).filter(r => r.proj.price != null || r.harv.price != null);

  const max = rows.reduce((m, r) => Math.max(m, r.proj.price || 0, r.harv.price || 0), 0);
  const money = (v) => v == null ? "—" : "$" + v.toFixed(2);
  const H = isMobile ? 150 : 200;
  const groupW = isMobile ? 78 : 104;
  const colW = isMobile ? 22 : 30;
  const ticks = [1, 0.5, 0];
  const barH = (v) => (v > 0 ? Math.max(2, (v / max) * H) : 0);
  const swatch = (c) => ({ width: 10, height: 10, borderRadius: 2, background: c, flex: "0 0 auto" });
  const chip = (on) => ({ padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit",
    border: "1px solid " + (on ? "#2d6a2d" : "#dde7dd"), background: on ? "#2d6a2d" : "#fff",
    color: on ? "#fff" : "#4a6e4a", fontSize: 12, fontWeight: 700 });
  const th = { textAlign: "right", padding: "7px 12px", fontSize: 10, fontWeight: 800, color: "#60735f",
    textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" };
  const td = { textAlign: "right", padding: "9px 12px", fontSize: 13, color: "#1a2e1a",
    borderTop: "1px solid #f0f4f0", whiteSpace: "nowrap" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.07em", marginRight: 4 }}>Crop</span>
        {withData.map(c => (
          <button key={c} onClick={() => setCrop(c)} style={chip(c === sel)}>{c === "WHEAT" ? "WHEAT (SRW)" : c}</button>
        ))}
      </div>

      {!rows.length ? (
        <Card style={{ color: "#60735f", fontSize: 13 }}>No price history on file for {sel}.</Card>
      ) : (
        <>
          <Card>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
                {sel === "WHEAT" ? "Wheat (SRW)" : sel[0] + sel.slice(1).toLowerCase()} — projected vs harvest
              </div>
              <span style={{ fontSize: 11.5, color: "#60735f" }}>all figures $/{unit}</span>
            </div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", margin: "8px 0 12px", fontSize: 11.5, color: "#4a6e4a" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={swatch(PA_PRIOR)} />Projected</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={swatch(PA_NOW)} />Harvest</span>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ width: isMobile ? 40 : 50, flex: "0 0 auto", height: H, position: "relative" }}>
                {ticks.map(t => (
                  <div key={t} style={{ position: "absolute", top: (1 - t) * H - 6, right: 0, fontSize: 10, color: "#677167" }}>
                    ${(max * t).toFixed(max >= 100 ? 0 : 2)}
                  </div>
                ))}
              </div>
              <div style={{ flex: 1, minWidth: 0, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 4 }}>
                <div style={{ position: "relative", minWidth: rows.length * groupW }}>
                  {ticks.map(t => (
                    <div key={t} style={{ position: "absolute", left: 0, right: 0, top: (1 - t) * H,
                                          borderTop: t === 0 ? "1px solid #c9d8c9" : "1px solid #eef3ee" }} />
                  ))}
                  <div style={{ display: "flex", alignItems: "flex-end", height: H, position: "relative" }}>
                    {rows.map(r => (
                      <div key={r.year} style={{ width: groupW, flex: "0 0 auto", display: "flex", justifyContent: "center", alignItems: "flex-end" }}>
                        {/* 2px surface gap between the pair — a gap, never a border */}
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                          <div title={`${r.year} projected: ${money(r.proj.price)}/${unit}`}
                               style={{ width: colW, height: barH(r.proj.price), background: PA_PRIOR, borderRadius: "4px 4px 0 0" }} />
                          {r.harv.price != null
                            ? <div title={`${r.year} harvest: ${money(r.harv.price)}/${unit}${r.harWins ? " — lifted the guarantee" : ""}`}
                                   style={{ width: colW, height: barH(r.harv.price), background: PA_NOW, borderRadius: "4px 4px 0 0" }} />
                            : <div title={`${r.year} harvest: not published`}
                                   style={{ width: colW, height: 2, background: "#dde7dd" }} />}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Direct labels on every bar — the light shade is under 3:1 on
                      this surface, so the figures do the work colour can't. */}
                  <div style={{ display: "flex" }}>
                    {rows.map(r => (
                      <div key={r.year} style={{ width: groupW, flex: "0 0 auto", paddingTop: 6, textAlign: "center" }}>
                        <div style={{ display: "flex", justifyContent: "center", gap: 2, fontSize: 10.5, fontWeight: 700 }}>
                          <span style={{ width: colW, color: "#5a7a5a" }}>{money(r.proj.price)}</span>
                          <span style={{ width: colW, color: r.harv.price == null ? "#c0c8c0" : "#1a2e1a" }}>
                            {r.harv.price == null ? "—" : money(r.harv.price)}
                          </span>
                        </div>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: "#1a2e1a", marginTop: 3 }}>{r.year}</div>
                        {r.harWins && <div style={{ fontSize: 10, fontWeight: 700, color: "#2d6a2d" }}>harvest won</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#677167", marginTop: 12, lineHeight: 1.6 }}>
              Same measure at two points in the season, so one colour in two shades. A missing harvest bar
              means RMA publishes no harvest price for that crop and year — the projected price is the guarantee.
            </div>
          </Card>

          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "#f4f8f4", borderBottom: "1px solid #e4ece4", fontWeight: 800, fontSize: 13, color: "#1a2e1a" }}>
              {sel === "WHEAT" ? "Wheat (SRW)" : sel[0] + sel.slice(1).toLowerCase()} by year
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
                <thead><tr style={{ background: "#fafcfa" }}>
                  <th style={{ ...th, textAlign: "left" }}>Year</th>
                  <th style={th}>Projected</th>
                  <th style={th}>Harvest</th>
                  <th style={th}>Guarantee</th>
                  <th style={th}>vs prior</th>
                </tr></thead>
                <tbody>
                  {rows.slice().reverse().map((r, i, arr) => {
                    const prev = arr[i + 1];
                    const d = prev && prev.eff != null && r.eff != null ? r.eff - prev.eff : null;
                    return (
                      <tr key={r.year} style={{ background: r.year === CURRENT_CROP_YEAR ? "#f4fbf4" : "#fff" }}>
                        <td style={{ ...td, textAlign: "left", fontWeight: r.year === CURRENT_CROP_YEAR ? 800 : 600 }}>
                          {r.year}{r.year === CURRENT_CROP_YEAR ? " ★" : ""}
                        </td>
                        <td style={{ ...td, color: "#5a7a5a" }}>{money(r.proj.price)}</td>
                        <td style={{ ...td, fontWeight: r.harWins ? 800 : 400, color: r.harv.price == null ? "#b0b8b0" : "#1a2e1a" }}>
                          {r.harv.price == null ? "not published" : money(r.harv.price)}
                        </td>
                        <td style={{ ...td, fontWeight: 800, color: "#2d7a2d" }}>{money(r.eff)}</td>
                        <td style={{ ...td, color: d == null ? "#b0b8b0" : d > 0 ? "#2d6a2d" : d < 0 ? "#a33" : "#60735f" }}>
                          {d == null ? "—" : (d > 0 ? "+" : "") + "$" + d.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRICE SCREEN
// ─────────────────────────────────────────────────────────────────────────────
function PriceScreen({ pricesByYear, onSave, dryBeanPrices, onSaveDryBeans, isMobile }) {
  // All NY-relevant spring/fall insurable crops we want shown on the price
  // reference page. Order matches how Mike thinks about the book: grains first,
  // then specialty/vegetable crops. Units come from SPRING_PRICE_UNIT (default
  // $/bu).
  const SPRING_CROPS = ["CORN", "SOYBEANS", "WHEAT", "BARLEY", "OATS", "TRITICALE", "GRAIN SORGHUM", "POTATOES", "CABBAGE", "SWEET CORN", "PROCESSING BEANS"];
  const cropUnit = (crop) => SPRING_PRICE_UNIT[crop] || "bu";
  const DB_TYPES = [
    { key: "BTS",    label: "Black Turtle (BTS)",       hasRevenue: true  },
    { key: "DRK",    label: "Dark Red Kidney (DRK)",    hasRevenue: true  },
    { key: "LRK",    label: "Light Red Kidney (LRK)",   hasRevenue: false },
    { key: "CBRY",   label: "Cranberry (CBRY)",         hasRevenue: false },
    { key: "PNAV",   label: "Navy/Pinto (PNAV)",        hasRevenue: true  },
    { key: "SMR",    label: "Small Red (SMR)",           hasRevenue: true  },
    { key: "WK",     label: "White Kidney (WK)",        hasRevenue: false },
    { key: "GRNOTH", label: "Other (GRNOTH)",           hasRevenue: false },
  ];

  const allYears = Object.keys(RMA_SPRING_PRICES).map(Number).sort((a,b) => b-a);
  const [selYear, setSelYear]       = useState(CURRENT_CROP_YEAR);
  const [dbLocal, setDbLocal]       = useState({ ...dryBeanPrices });
  const [dbSaved, setDbSaved]       = useState(false);
  const [tab, setTab]               = useState("spring");

  function handleSaveDryBeans() {
    onSaveDryBeans(dbLocal);
    setDbSaved(true);
    setTimeout(() => setDbSaved(false), 2500);
  }

  const T = (active) => ({
    padding: "9px 20px", border: "none", background: "none", cursor: "pointer",
    borderBottom: active ? "3px solid #2d4a2d" : "3px solid transparent",
    color: active ? "#1a2e1a" : "#60735f", fontWeight: active ? 800 : 500, fontSize: 13, marginBottom: -1,
  });

  const sp = year => RMA_SPRING_PRICES[year] || {};
  const hp = year => RMA_HARVEST_PRICES[year] || {};
  const eff = (crop, year) => effectivePrice(crop, year);

  // Daily bars for the contracts whose discovery windows belong to this crop
  // year, so a price RMA has not announced yet still shows a live running
  // average instead of a blank. One fetch per year, keyed by contract symbol.
  const [bars, setBars] = useState(null);
  useEffect(() => {
    let alive = true;
    const syms = [...new Set(Object.keys(PRICE_DISCOVERY)
      .map(c => discoveryContractSym(c, selYear)).filter(Boolean))];
    if (!syms.length) { setBars({}); return; }
    sbFetch("futures_contract_history", {
      select: "contract_sym,bar_date,close",
      filters: [`contract_sym=in.(${syms.join(",")})`], order: "bar_date.asc",
    }).then(rows => {
      if (!alive) return;
      const m = {};
      for (const r of rows || []) (m[r.contract_sym] = m[r.contract_sym] || []).push(r);
      setBars(m);
    }).catch(() => { if (alive) setBars({}); });
    return () => { alive = false; };
  }, [selYear]);

  // One row model per crop, straight off the shared ladder — so this page can
  // never drift from what the farmer cards and the quoter believe.
  const barsFor = (crop) => {
    const sym = discoveryContractSym(crop, selYear);
    return sym && bars ? (bars[sym] || null) : null;
  };
  const rowFor = (crop) => {
    const b = barsFor(crop);
    const projected = resolveRmaPrice(crop, selYear, "projected", b);
    const harvest   = resolveRmaPrice(crop, selYear, "harvest", b);
    const p = projected.price, h = harvest.price;
    const effective = p == null ? (h || null) : (h == null ? p : Math.max(p, h));
    return { crop, projected, harvest, effective, harWins: h != null && p != null && h > p };
  };
  const rows = SPRING_CROPS.map(rowFor);
  const conflicts = rows.flatMap(r => [
    r.projected.conflict ? { crop: r.crop, kind: "projected", ...r.projected.conflict } : null,
    r.harvest.conflict   ? { crop: r.crop, kind: "harvest",   ...r.harvest.conflict   } : null,
  ].filter(Boolean));
  const counts = {
    rma:      rows.filter(r => r.projected.source === "rma" || r.harvest.source === "rma").length,
    tracking: rows.filter(r => r.projected.status === "estimate" || r.harvest.status === "estimate").length,
    pending:  rows.filter(r => r.harvest.status === "pending" && r.projected.status !== "pending").length,
  };

  // Where a number came from, said in one chip. Colour carries the confidence:
  // green = RMA has announced it, amber = we're tracking the board, grey = it's
  // the figure we entered by hand and RMA hasn't published a machine-readable one.
  const SRC_CHIP = {
    rma:      { label: "RMA",       bg: "#eaf6e6", fg: "#256b25", bd: "#bcdcb4", title: "Read straight from RMA's Actuarial Data Master" },
    book:     { label: "verified",  bg: "#f2f6f2", fg: "#5a6a5a", bd: "#dde7dd", title: "Entered by hand from the RMA bulletin and checked" },
    futures:  { label: "tracking",  bg: "#fdf6e7", fg: "#7a5b00", bd: "#e8dca8", title: "Running average of the discovery window — not final" },
  };
  const srcChip = (res) => {
    const c = SRC_CHIP[res.source];
    if (!c) return null;
    return (
      <span title={c.title + (res.released ? ` · released ${res.released}` : "")}
        style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase",
                 background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, borderRadius: 9, padding: "1px 7px", marginLeft: 7, whiteSpace: "nowrap" }}>
        {c.label}
      </span>
    );
  };
  // The cell itself: the number, its provenance, and — when we're still tracking
  // — how far into the window we are. "14 of 22 days" is the difference between a
  // number worth quoting and one that is still moving.
  const priceCell = (res, unit) => {
    if (res.status === "pending") {
      const w = res.window;
      return (
        <div style={{ color: "#b0b8b0", fontSize: 12, fontStyle: "italic" }}>
          {w ? `opens ${w.from}` : "not published"}
        </div>
      );
    }
    return (
      <div>
        <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: res.status === "estimate" ? "#7a5b00" : "#1a2e1a" }}>
            ${res.price.toFixed(2)}<span style={{ fontSize: 11, color: "#677167" }}>/{unit}</span>
          </span>
          {srcChip(res)}
        </div>
        {res.status === "estimate" && (
          <div style={{ fontSize: 10.5, color: "#9a8a5a", marginTop: 2 }}>
            {res.days} of {res.expectedDays} days{res.closed ? " · window closed, awaiting RMA" : " · still moving"}
          </div>
        )}
        {res.source === "rma" && res.released && (
          <div style={{ fontSize: 10.5, color: "#677167", marginTop: 2 }}>released {res.released}</div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <h2 style={{ ...uiPageTitle(isMobile) }}>RMA Prices</h2>
          {_rmaFeedMeta.loadedAt && (
            <span style={{ fontSize: 11, color: "#60735f" }}>
              ADM feed · {_rmaFeedMeta.rows.toLocaleString()} rows
            </span>
          )}
        </div>
        <p style={{ margin: "5px 0 0", color: "#60735f", fontSize: 12.5, lineHeight: 1.6, maxWidth: 760 }}>
          Every price here resolves the same way, and the page tells you which rung it landed on:
          RMA's published number when there is one, the figure we verified by hand when there isn't,
          and a live average of the open discovery window when RMA hasn't announced yet.
          The guarantee is always the higher of projected and harvest.
        </p>
      </div>

      {/* What's settled and what isn't — the question you open this page with. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
        {[
          [counts.rma,      "from RMA",        "#eaf6e6", "#256b25", "#bcdcb4"],
          [counts.tracking, "tracking a board", "#fdf6e7", "#7a5b00", "#e8dca8"],
          [counts.pending,  "not open yet",    "#f4f6f4", "#60735f", "#e0e6e0"],
        ].filter(([n]) => n > 0).map(([n, label, bg, fg, bd]) => (
          <div key={label} style={{ background: bg, color: fg, border: `1px solid ${bd}`, borderRadius: 10, padding: "7px 14px", fontSize: 12.5, fontWeight: 700 }}>
            {n} <span style={{ fontWeight: 500 }}>{label}</span>
          </div>
        ))}
      </div>

      {/* A refused feed value. Loud on purpose: this is the one failure mode that
          could otherwise move a revenue guarantee without anyone noticing. */}
      {conflicts.length > 0 && (
        <Card style={{ marginBottom: 16, background: "#fdf6f6", borderColor: "#e6c9c9" }}>
          <div style={{ fontWeight: 800, color: "#8a3a3a", fontSize: 13, marginBottom: 6 }}>
            ⚠ RMA figures disagree on {conflicts.length} price{conflicts.length === 1 ? "" : "s"}
          </div>
          <div style={{ fontSize: 12.5, color: "#8a3a3a", lineHeight: 1.7 }}>
            {conflicts.map(c => (
              <div key={c.crop + c.kind}>
                <b>{c.crop}</b> {c.kind}: {rmaConflictText(c)}.
              </div>
            ))}
            <div style={{ marginTop: 6, color: "#7a5a5a" }}>
              The figure already in use stays, and nothing is swapped in silently. Against a verified
              constant, a feed value this far out is more likely a wrong ADM commodity code or a unit
              mismatch than a real move; against our settled discovery average or RMA's approved
              figure, guarantees built on the price are withheld until someone checks the bulletin.
            </div>
          </div>
        </Card>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", borderBottom: "1px solid #e4ece4", marginBottom: 20 }}>
        <button style={T(tab === "spring")} onClick={() => setTab("spring")}>🌱 Spring / Fall Prices</button>
        <button style={T(tab === "history")} onClick={() => setTab("history")}>📈 Price History</button>
        <button style={T(tab === "drybeans")} onClick={() => setTab("drybeans")}>🫘 Dry Bean Prices</button>
      </div>

      {tab === "history" && (
        <RmaPriceHistory crops={SPRING_CROPS} years={allYears} cropUnit={cropUnit} isMobile={isMobile} />
      )}

      {tab === "spring" && (
        <div>
          {/* Year selector + RMA reset */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
            {allYears.map(y => (
              <button key={y} onClick={() => setSelYear(y)} style={{
                padding: "7px 18px", borderRadius: 20, border: "none",
                background: selYear === y ? "#2d4a2d" : "#e8f0e8",
                color: selYear === y ? "#fff" : "#2d4a2d",
                fontWeight: 800, fontSize: 13, cursor: "pointer",
              }}>{y}</button>
            ))}
            {RMA_SPRING_PRICES[selYear] && (
              <button
                onClick={async () => {
                  if (!window.confirm(`Reset ${selYear} prices to official RMA projected values?`)) return;
                  await onSave(selYear, { ...RMA_SPRING_PRICES[selYear] });
                }}
                style={{ marginLeft: 8, padding: "7px 16px", borderRadius: 20, border: "1px solid #c8860a", background: "#fffbf0", color: "#7a4a00", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
              >↺ Reset {selYear} to RMA Official</button>
            )}
          </div>

          {/* Price table for selected year */}
          <Card style={{ marginBottom: 16, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 18px", background: "#f4f8f4", borderBottom: "1px solid #e4ece4", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 800, fontSize: 14, color: "#1a2e1a" }}>{selYear} Crop Year — Official RMA Prices</span>
              {selYear === CURRENT_CROP_YEAR && <span style={{ fontSize: 11, background: "#eef6ec", border: "1px solid #4a7a2a44", borderRadius: 10, padding: "2px 10px", color: "#2d5a10", fontWeight: 700 }}>CURRENT YEAR</span>}
            </div>
            {/* On a phone the four columns scroll sideways inside the card rather than
                crushing into each other — at 390px the prices ran together. */}
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <div style={{ minWidth: isMobile ? 600 : undefined }}>
            {/* Header row */}
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr", gap: 0, padding: "8px 18px", background: "#fafcfa", borderBottom: "1px solid #edf2ed" }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.08em" }}>Crop</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.08em" }}>Spring / Fall Projected</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.08em" }}>Harvest Price</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.08em" }}>Effective Guarantee</div>
            </div>
            {rows.map(({ crop, projected, harvest, effective, harWins }) => {
              const color = CROP_COLOR[crop] || "#666";
              const bg    = CROP_BG[crop]    || "#f4f8f4";
              const cropLabel = crop === "WHEAT" ? "WHEAT (SRW)" : crop;
              const cabbageVariants = crop === "CABBAGE" ? CABBAGE_PRICES[selYear] : null;
              const unit = cropUnit(crop);
              // A crop with no futures board and no published election has no
              // harvest price at all — that's "n/a", a different thing from a
              // window that hasn't opened yet, and the two used to look alike.
              const noHarvestConcept = harvest.status === "pending" && !harvest.window;
              return (
                <div key={crop} style={{ borderBottom: "1px solid #f0f4f0" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr", padding: "14px 18px", alignItems: "start" }}>
                    <div style={{ paddingTop: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color, background: bg, border: "1px solid " + color + "44", padding: "3px 8px", borderRadius: 4 }}>{cropLabel}</span>
                    </div>
                    <div>
                      {priceCell(projected, unit)}
                      {crop === "CABBAGE" && <div style={{ fontSize: 10, color: "#60735f", marginTop: 2 }}>fresh conv. — variants below</div>}
                    </div>
                    <div>
                      {noHarvestConcept
                        ? <span style={{ fontSize: 11, color: "#b0b8b0", fontStyle: "italic" }}>n/a — no harvest price on this crop</span>
                        : priceCell(harvest, unit)}
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: "#2d7a2d", paddingTop: 1 }}>
                      {effective != null ? `$${effective.toFixed(2)}/${unit}` : "—"}
                      {harWins && <span style={{ marginLeft: 6, fontSize: 10, background: "#eef6ec", borderRadius: 3, padding: "2px 5px", color: "#2d5a10" }}>HARVEST WINS</span>}
                      {(projected.status === "estimate" || harvest.status === "estimate") && (
                        <div style={{ fontSize: 10.5, color: "#9a8a5a", fontWeight: 600, marginTop: 2 }}>moves until RMA announces</div>
                      )}
                    </div>
                  </div>
                  {cabbageVariants && (
                    <div style={{ padding: "8px 18px 14px 18px", background: "#fafcfa" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Cabbage variants ($/cwt)</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, fontSize: 12 }}>
                        {[
                          ["Fresh · Conventional", cabbageVariants.FRESH_CONVENTIONAL],
                          ["Fresh · Organic",      cabbageVariants.FRESH_ORGANIC],
                          ["Processing · Conv.",   cabbageVariants.PROC_CONVENTIONAL],
                          ["Processing · Organic", cabbageVariants.PROC_ORGANIC],
                        ].map(([label, val]) => (
                          <div key={label} style={{ background: "#fff", border: "1px solid #e4ece4", borderRadius: 6, padding: "6px 10px" }}>
                            <div style={{ fontSize: 10, color: "#60735f", marginBottom: 2 }}>{label}</div>
                            <div style={{ fontWeight: 800, fontSize: 14, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>${val?.toFixed(2) ?? "—"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            </div>
            </div>
          </Card>

          {/* The all-years crosstab moved to its own Price History tab.
              Eleven crops side by side meant 22 columns behind a scrollbar,
              and every blank harvest cell said "TBD" about seasons that
              closed years ago. It reads one crop at a time now. */}
        </div>
      )}

      {tab === "drybeans" && (
        <div>
          <Card style={{ marginBottom: 16, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 18px", background: "#f4f8f4", borderBottom: "1px solid #e4ece4", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: 14, color: "#1a2e1a" }}>Dry Bean Prices — Global ($/lb)</span>
                <div style={{ fontSize: 11, color: "#60735f", marginTop: 2 }}>Applied to all farmers. RMA posts prices per type annually. Types marked ★ have revenue protection (harvest price applies).</div>
              </div>
              <button onClick={handleSaveDryBeans} style={{ padding: "9px 22px", background: "#2d4a2d", color: "#fff", border: "none", borderRadius: 7, fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
                {dbSaved ? "✓ SAVED" : "SAVE"}
              </button>
            </div>
            {DB_TYPES.map(({ key, label, hasRevenue }) => {
              const price = dbLocal[key] ?? 0;
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 18px", borderBottom: "1px solid #f0f4f0" }}>
                  <div style={{ width: 200, flexShrink: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: CROP_COLOR["DRY BEANS"], background: CROP_BG["DRY BEANS"], border: "1px solid " + CROP_COLOR["DRY BEANS"] + "44", padding: "2px 8px", borderRadius: 4 }}>
                      {label}{hasRevenue ? " ★" : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "#60735f" }}>$</span>
                    <input type="number" step="0.01" min="0" value={price || ""} onChange={e => { setDbLocal(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 })); setDbSaved(false); }}
                      placeholder="0.00"
                      style={{ width: 90, padding: "9px 10px", border: "2px solid " + (price > 0 ? CROP_COLOR["DRY BEANS"] + "88" : "#e0e0e0"), borderRadius: 7, fontSize: 15, fontWeight: 700, color: "#1a2e1a", outline: "none", textAlign: "right" }} />
                    <span style={{ fontSize: 12, color: "#60735f" }}>/ lb</span>
                  </div>
                  {!hasRevenue && <span style={{ fontSize: 11, color: "#b0b8b0", fontStyle: "italic" }}>No harvest price adjustment</span>}
                  {hasRevenue && <span style={{ fontSize: 11, color: "#4a7a2a" }}>★ Revenue protection — harvest price applies</span>}
                </div>
              );
            })}
          </Card>
          <div style={{ fontSize: 12, color: "#60735f", padding: "0 4px" }}>
            ★ Types with true revenue protection: BTS, DRK, PNAV (Navy), SMR — harvest price determined in January following harvest.
            All others use a single price set at the start of the crop year.
          </div>
        </div>
      )}
    </div>
  );
}

// RESET A FARMER'S DRP DATA IN SUPABASE
// ─────────────────────────────────────────────────────────────────────────────
async function resetFarmerDRP(farmer) {
  // farmer may have multiple DB rows (e.g. 2025 and 2026 SOIs created separate farmer rows).
  // allFarmerDbIds contains ALL farmer_id values that share this name — reset all of them.
  const farmerIds = farmer.allFarmerDbIds?.length ? farmer.allFarmerDbIds : [farmer.id];
  for (const fid of farmerIds) {
    const polRes = await fetch(
      `${SUPABASE_URL}/rest/v1/policies?farmer_id=eq.${fid}&select=id`,
      { headers: getAuthHeaders() }
    );
    const policies = await polRes.json();
    for (const pol of (policies || [])) {
      await sbDelete('drp_endorsements', `policy_id=eq.${pol.id}`);
      await sbDelete('drp_policies',     `policy_id=eq.${pol.id}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF BATCH UPLOAD + AI EXTRACTION
// ─────────────────────────────────────────────────────────────────────────────

const EXTRACTION_PROMPT = `You are reading a crop insurance document. It may be from NAU Country Insurance OR Rain and Hail / ACE Property and Casualty Insurance Company. Extract data and return ONLY valid JSON with no markdown, no explanation, no extra text.

DOCUMENT IDENTIFICATION GUIDE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
NAU Country / QBE Insurance documents:
  • "Report of Certified Acres (Planted and Prevented)" → documentType: PP_REPORT (aip: NAU)
  • "Actual Production History (APH) Database" → documentType: APH_DATABASE (aip: NAU)
  • "Schedule of Insurance" with crop rows (corn/soybeans/wheat/beans) → documentType: SOI_CROP (aip: NAU)
  • "Schedule of Insurance" with DRP / milk endorsements → documentType: SOI_DRP (aip: NAU)
  • "Pasture Rangeland Forage" / PRF / Rainfall Index → documentType: PRF (aip: NAU)
  • LRP endorsements → documentType: LRP (aip: NAU)

Rain & Hail / ACE Property and Casualty Insurance documents (form numbers RH-xxxx):
  • RH-5200 "MPCI Application/Cancellation/Transfer/Policy Change/Reporting Form"
    + RH-5204 "MPCI Acreage and Production Reporting Form" (page 3 of same packet)
    → documentType: APH_DATABASE (aip: RAIN_AND_HAIL)
  • RH-5106 "Prevented Planting Worksheet" (ACE Property and Casualty) → documentType: PP_REPORT (aip: RAIN_AND_HAIL)
  • "MPCI Prevented Planting Acres" batch report (multiple farmers, Rain & Hail, header says "ACE PROPERTY AND CASUALTY INSURANCE COMPANY", lists many farmers with Name/Policy/County/Crop/Plan/Max Eligible Acres/Remaining Eligible Acres columns) → documentType: BATCH_PP (aip: RAIN_AND_HAIL)
  • "Schedule of Insurance" / "Summary of Coverage" with crop rows → documentType: SOI_CROP (aip: RAIN_AND_HAIL)
  • "MAP BASED PRODUCTION REPORTING AND SUMMARY OF INFORMATION" (RH-1880P) → documentType: SOI_CROP (aip: RAIN_AND_HAIL)
  • "2026 SUMMARY OF INFORMATION" without "MAP BASED" subtitle (compact tabular format, has COVERAGE table + detail YLD# rows, policy MP-XXXXXXX) → documentType: SOI_CROP (aip: RAIN_AND_HAIL) — see section 4c
  • "Schedule of Insurance" with DRP / dairy milk endorsements → documentType: SOI_DRP (aip: RAIN_AND_HAIL)
  • LRP "Livestock Risk Protection" endorsement pages → documentType: LRP (aip: RAIN_AND_HAIL)

KEY DISTINGUISHING FEATURES:
  • NAU APH: header says "Actual Production History (APH) Database", has "Apprv Yld" column
  • R&H APH: form number RH-5200/5204, has "Appr Yld" at bottom of each unit block
  • NAU PP: "Report of Certified Acres", table with 4 prior year acre columns
  • R&H PP: form RH-5106 "Prevented Planting Worksheet", has "SUMMARY OF POLICY UNITS - BY PP LIABILITY" section
  • SOI Crop: header says "Schedule of Insurance", rows have county/crop/plan/coverage/approved yield/acres
  • SOI DRP: "Schedule of Insurance" or "Summary of Coverage" with milk lbs, Class III/IV, trigger price
  • PRF: "Pasture Rangeland Forage", rainfall index intervals (MAYJUN, JULAUG etc)
  • LRP: "Livestock Risk Protection", cattle endorsements with coverage price per cwt
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

DOCUMENT TYPES AND WHAT TO EXTRACT:

1. NAU "Report of Certified Acres" (PP Report):
   - Header: Insured name, policy number (e.g. NY-944-6076784-26), crop year, agent, agency, insurer
   - Table rows: each row has County code+name, Contract Origin, Crop name, Practice, Type, and 4 year acre columns
   - Example row: "073 - Orleans R 0041 - Corn Non-Irrigated GSG 728.92 870.41 661.74 918.05"
   - Extract county as just the name (e.g. "Orleans"), crop as uppercase (e.g. "CORN", "SOYBEANS", "DRY BEANS")
   - CROP NAME NORMALIZATION: use ONLY the base crop name, uppercase:
     * "0041 - Corn" or "0041 - Corn Organic(Certified) Non-Irr." → "CORN"
     * "0081 - Soybeans" or "0081 - Soybeans Organic(Certified) Non-Irr." → "SOYBEANS"
     * "0011 - Wheat" or "0011 - Wheat Organic(Certified) Non-Irr." → "WHEAT"
     * "0047 - Dry Beans" → "DRY BEANS"
     * "0046 - Processing Beans" → "PROCESSING BEANS"
     * "0051 - Grain Sorghum" → "GRAIN SORGHUM"
     * "0084 - Potatoes" or "0084 - Potatoes (Processing)" or "0084 - Potatoes (Fresh)" or "0093 - Potatoes" → "POTATOES"
     * Strip "Organic(Certified)" and "Non-Irr." from crop names entirely — use only the base crop
   - TYPE CODES you will see: GSG, GSS, NTS, W, BTS, CBRY, LRK, DRK, SMR, PNAV, WK, PTO, GRNOTH, ALL
     PTO = Potatoes (fresh/tablestock), CHIP = Potatoes (chipping/processing), SEED = Certified seed potatoes
     ALL = Potatoes with no sub-type distinction (NAU uses this when no type breakdown is specified)
   - The 4 columns are the most recent 4 crop years shown in the column header row (e.g. 2022 2023 2024 2025)
   - Map them to yr1/yr1Year, yr2/yr2Year, yr3/yr3Year, yr4/yr4Year chronologically
   - Strip asterisks from values (918.05* → 918.05), strip commas from large values (1,643.91 → 1643.91)
   - Acres marked 0.00* still count as 0
   - Only extract the crop-level rows (e.g. "0041 - Corn"), NOT the "@ Corn Prior Year Totals" summary rows
   - EMPTY TABLE / UNREADABLE: if year columns show "????" or there are no data rows, output ppEligibility=[]
   - Multi-page policy: if the document spans multiple pages for the same policy number, combine ALL rows into one ppEligibility array
   - documentType: "PP_REPORT"

2. Rain & Hail "Prevented Planting Worksheet" (ACE Property and Casualty):
   - Header (first few lines): Insured name on line 4 (after SCAN barcode, "ACE PROPERTY AND CASUALTY INSURANCE COMPANY", year+policy#, page#)
     May include "DBA" on next line. Policy number format: MP-XXXXXXX (e.g. MP-0843027). Crop year = 4-digit year in header.
   - Strip -RP/-YP/-APH suffixes from crop names everywhere: CORN-RP → CORN, SOYBEANS-RP → SOYBEANS, DRY BEANS-APH → DRY BEANS
   - The document has TWO sections to parse:

   SECTION A — TOP TABLE: "MAXIMUM INC INC MAX PLANTED ELIGIBLE REPORTED PP REMAINING"
   Header columns: CROP | COUNTY | ELIG ACRES | FACTOR | ELIG ACRES | ACRES | PP ACRES | ACRES | ACRES | PP
   Row types in this section:
     • CROP-level rows: "CORN-RP ORLEANS  1303.50(2025)  x 1 = 1303.50  ..."
       The number before (year) is maxEligAcres, the year in () is maxEligYear
     • TYPE sub-rows: "TYPE GSG ORLEANS  1303.50(2025)  x 1 = ..."
       These give the type-specific maxEligAcres — PREFER these over the crop-level row when present
     • PRAC sub-rows: "PRAC NON-IRR COUNTY ..." — skip, don't use
   Strategy for maxEligAcres: for each county+crop+type combination, use the TYPE sub-row's value.
     If no TYPE sub-row exists (e.g. SOYBEANS/NTS, WHEAT/W), use the CROP-level row's value.
     If value is 0.00 or (N/A), set maxEligAcres=0 and maxEligYear=null.

   SECTION B — BOTTOM TABLE: "SUMMARY OF POLICY UNITS - BY PP LIABILITY"
   Columns: CROP | COUNTY | PRACTICE | TYPE | UNIT# | UNIT DESCRIPTION | LEGAL | YIELD # | PER ACRE
   IMPORTANT RULES FOR THIS TABLE:
     • YIELD # is a sequential unit index number (1.0, 2.0, ... 10.0) — NOT a bu/acre yield. NEVER use as aphYield.
     • PER ACRE is the PP LIABILITY PER ACRE in dollars (e.g. 296.82). Use DIRECTLY — never recalculate.
     • If table is empty (no data rows before "*** 2026 Units Reported..."), set ppEligibility = [].
     • Each data row: CROP-PLAN COUNTY PRACTICE TYPE UNIT# UNITTYPE FSN:xxx ... YIELD# PER_ACRE
       Where UNITTYPE = BU (basic unit), OU (optional unit), EU (enterprise unit)
     • TYPE column values you will see: GSG, GSS, NTS, W, BTS, CBRY, DRK, LRK
       GSG = Grain Sorghum/Grain type, GSS = Grain Sorghum/Silage, NTS = Non-Traditional Seed,
       W = Wheat, BTS = Black Turtle Soup bean, CBRY = Cranberry bean, DRK = Dark Red Kidney, LRK = Light Red Kidney
     • PRACTICE values: "NON IRR" (non-irrigated) or "NON-IRROC" (non-irrigated organic certified)
       Normalize to: "Non-Irrigated" for NON IRR, "Organic" for NON-IRROC
     • MULTIPLE ROWS FOR SAME CROP IN SAME COUNTY: These MUST be kept as separate entries.
       Example: CORN/GSG and CORN/GSS in same county = two different ppEligibility rows with different per_acre values.
       NEVER merge them. NEVER average them. They represent different practice types with different guarantees.
     • OPTIONAL UNITS (OU): Multiple rows with same county+crop+type but different unit numbers (e.g. 1.0001 through 1.0010).
       These are individual field units. Collapse to ONE ppEligibility entry per county+crop+type, using the MAXIMUM PER ACRE value.
       Example: 10 rows of CORN/GSG/CHEMUNG with per_acre values $261-$326 → one entry with ppLiabilityPerAcre=$326.17
     • ZERO PER ACRE (0.00): Still create the ppEligibility row. Set ppLiabilityPerAcre=null (not 0).
     • DRY BEANS rows: may have types BTS (always 0.00), CBRY, DRK, or LRK — extract each as a separate row.
     • MULTI-COUNTY: Process ALL counties present. Each county+crop+type = separate ppEligibility row.

   OUTPUT — for each unique county+crop+type+practice combination:
   {
     county: county name exactly as shown (e.g. "ORLEANS", "GENESEE"),
     crop: uppercase, suffix stripped ("CORN", "SOYBEANS", "DRY BEANS", "WHEAT"),
     practice: "Non-Irrigated" for conventional (NON IRR), "Organic" for certified organic (NON-IRROC),
     type: type code exactly ("GSG", "GSS", "NTS", "W", "BTS", "CBRY", "DRK", "LRK"),
     yr1: null, yr2: null, yr3: null,
     yr4: maxEligAcres from Section A TYPE row (or CROP row if no TYPE row), or null if 0/(N/A),
     yr4Year: maxEligYear from Section A (the year in parentheses), or null,
     maxEligAcres: same as yr4,
     maxEligYear: same as yr4Year,
     aphYield: null,
     coverageLevel: null,
     ppLiabilityPerAcre: PER ACRE value from Section B if > 0, else null
   }

   WORKED EXAMPLES:
   Example A — Bentley Eric D (MP-0843027, Orleans county):
     Section A TYPE rows: GSG/ORLEANS=560.90(2023), GSS/ORLEANS=0.00(N/A), SOYBEANS/ORLEANS=560.40(2024)
     Section B rows: CORN/GSS/ORLEANS=$296.82, CORN/GSG/ORLEANS=$341.42, SOYBEANS/NTS/ORLEANS=$269.49
     Output 3 entries:
       {county:"ORLEANS", crop:"CORN", type:"GSS", yr4:0, yr4Year:null, ppLiabilityPerAcre:296.82}
       {county:"ORLEANS", crop:"CORN", type:"GSG", yr4:560.90, yr4Year:2023, ppLiabilityPerAcre:341.42}
       {county:"SOYBEANS", crop:"SOYBEANS", type:"NTS", yr4:560.40, yr4Year:2024, ppLiabilityPerAcre:269.49}

   Example B — Burnham Tyler (MP-0856476, 10 OU units all CORN/GSG/CHEMUNG):
     Section A: TYPE GSG CHEMUNG = 242.50(2023)
     Section B: 10 rows, per_acre values $261.03 to $326.17, all same county+crop+type
     Output 1 entry: {county:"CHEMUNG", crop:"CORN", type:"GSG", yr4:242.50, yr4Year:2023, ppLiabilityPerAcre:326.17}

   Example C — Naas Farms LLC (MP-0811296, three counties, includes DRY BEANS/BTS with 0.00):
     Section B includes: DRY BEANS/BTS/GENESEE=$0.00, SOYBEANS/NTS/GENESEE=$245.09, CORN/GSG/GENESEE=$312.77, etc.
     Output row for DRY BEANS/BTS/GENESEE: {county:"GENESEE", crop:"DRY BEANS", type:"BTS", ppLiabilityPerAcre:null}
     Output row for CORN/GSG/GENESEE: {county:"GENESEE", crop:"CORN", type:"GSG", ppLiabilityPerAcre:312.77}

   Example D — Empty table (e.g. Brian Thompson, Ernest Gates):
     Section B has no data rows (only "*** 2026 Units Reported...")
     Output: ppEligibility = []

   - For Rain & Hail PP docs, approvedYields = [] always
   - documentType: "PP_REPORT"

2b. Rain & Hail "MPCI Prevented Planting Acres" BATCH report:
   - Header says "MPCI Prevented Planting Acres", "ACE PROPERTY AND CASUALTY INSURANCE COMPANY"
   - This is a multi-farmer batch report. Each row group has: Name, Policy, County, Crop, Plan, Max Eligible Acres, Remaining Eligible Acres
   - Sub-rows indented under a crop row show Type (GSG, GSS, etc.) and Practice (NON-IRR) with their own acre values — use these for type/practice fields
   - CROP NAME NORMALIZATION: strip plan suffixes, uppercase: "Wheat RP" → "WHEAT", "Corn RP" → "CORN", "Soybeans RP" → "SOYBEANS", "Dry Beans APH" → "DRY BEANS"
   - A single farmer may appear multiple times with different policy numbers (e.g. KAST FARMS INC has MP-0835689 for spring and MP-0865595 for wheat) — keep these as SEPARATE farmer entries, one per policy number
   - For each unique Name+Policy combination, produce one farmer entry with all their county/crop rows as ppEligibility
   - cropYear: use the year shown in the document header (e.g. "2026 Crop Year" → 2026)
   - agent: null (do NOT fabricate — true agent of record is pulled from M13 P55/P11; manual imports must not stomp it), agency: "SPARTAN INS AGENCY LLC", aip: "RAIN_AND_HAIL"
   - For each ppEligibility row: { county, crop, plan, type (if shown), practice (if shown), maxEligAcres, remainingEligAcres }
     * maxEligAcres: the "Max Eligible Acres" column value
     * remainingEligAcres: the "Remaining Eligible Acres" column value  
     * yr1/yr2/yr3/yr4: null (batch report does not show year history)
     * ppLiabilityPerAcre: null (not shown in batch report)
     * aphYield: null, coverageLevel: null
   - Skip sub-rows that are ONLY Type/Practice detail lines with no new acre values different from parent — only include rows that represent a distinct crop+county+type combination
   - Output format (DIFFERENT from single-farmer docs — returns an array of farmers):
   {
     "documentType": "BATCH_PP",
     "aip": "RAIN_AND_HAIL",
     "cropYear": 2026,
     "farmers": [
       {
         "name": "ATWATER FARMS LLC",
         "policyNumber": "MP-0865508",
         "cropYear": 2026,
         "county": "NIAGARA",
         "state": "NY",
         "agent": null,
         "agency": "SPARTAN INS AGENCY LLC",
         "aip": "RAIN_AND_HAIL",
         "ppEligibility": [
           { "county": "NIAGARA", "crop": "WHEAT", "plan": "RP", "maxEligAcres": 609.50, "remainingEligAcres": 85.50, "yr1": null, "yr2": null, "yr3": null, "yr4": null, "ppLiabilityPerAcre": null },
           { "county": "ORLEANS", "crop": "WHEAT", "plan": "RP", "maxEligAcres": 80.20, "remainingEligAcres": 80.20, "yr1": null, "yr2": null, "yr3": null, "yr4": null, "ppLiabilityPerAcre": null }
         ]
       },
       ...one entry per unique Name+Policy combination...
     ]
   }
   - documentType: "BATCH_PP"

3. NAU "Actual Production History (APH) Database":
   - Header: Insured name, policy number, crop year, counties
   - Page 1 Summary of Coverage table: extract county, crop, practice/type, plan, coverageLevel for each RP row
     * IGNORE ECO-RP rows — ECO-RP is supplemental coverage, not the base policy
     * Only extract rows with Plan = RP, APH, YP, RP-HPE, etc. (anything except ECO-RP and SCO)
     * UNIT NUMBER tells you practice: unit 0001-0001-xxx = conventional (NON IRR → "Non-Irrigated"), unit 0001-0002-xxx = organic (NON-IRROC → "Organic"). Always read the Practice field in the block header to confirm, but use unit number as a guide.
   - Page 2 "Production Reporting Unit Summary" shows CURRENT YEAR production per acre — DO NOT use these as approved yields
     * This page just shows last year's actual production: acres, yield/acre, total production
     * The yield per acre shown here is NOT the approved yield — skip this entire page for yield extraction
     * CRITICAL: Do NOT extract acres from an APH document. The APH shows historical production acres, not 2026 insured planted acres.
       Insured planted acres only come from a Schedule of Insurance (SOI). For all approvedYields rows from an APH document, set acres=null.
   - Per-crop detail pages (pages 3+): NAU APH pages use a TWO-COLUMN layout — each physical page shows TWO unit blocks side by side (left column and right column)
     * MANDATORY FIRST STEP: Before extracting any yields, scan every detail page and list every block header you can see: County, Type, Unit#. A page with two columns has TWO headers. Do not proceed to yield extraction until you have inventoried all block headers across all pages.
     * Each column is an independent unit block with its own: County, Practice (NON IRR or NON-IRROC), Type (GSG, GSS, NTS, etc.), Unit #, history table, and "Apprv Yld" at the bottom
     * PRACTICE: Read the "Practice" field in each block header. "NON IRR" → practice="Non-Irrigated". "NON-IRROC" → practice="Organic". This MUST be extracted — organic and conventional are separate insured units with different approved yields.
     * CRITICAL: The TYPE of each block is determined by reading the "Type" field IN THAT BLOCK'S HEADER — do NOT assume the right column is GSS or the left column is GSG. The two columns can be any combination: GSG+GSG (two different counties), GSG+GSS (same county, different types), or GSS+GSS. Always read the Type field explicitly.
     * Example (Anderson Farms page 3): Left block header = County:051-Livingston, Type:GSG, Practice:NON IRR → Apprv Yld:180 → {county:"LIVINGSTON", crop:"CORN", type:"GSG", practice:"Non-Irrigated", approvedYield:180}. Right block header = County:051-Livingston, Type:GSS, Practice:NON IRR → Apprv Yld:16.8 → {county:"LIVINGSTON", crop:"CORN", type:"GSS", practice:"Non-Irrigated", approvedYield:16.8}. BOTH rows must appear — one page, two blocks, two output rows.
     * Example (Acomb page 3): Left block = County:003-Allegany, Type:GSG → Apprv Yld:171. Right block = County:051-Livingston, Type:GSG → Apprv Yld:171. Both GSG, different counties — produce two GSG rows, zero GSS rows for these counties.
     * The ONLY yield to extract from each block is the value explicitly labeled "Apprv Yld" or "Apprv Yield" — it may appear anywhere in the block summary section, not always at the bottom. Scan the entire block for this label.
     * Do NOT use any other yield field — not "Adj. Yield", "Rate Yld", "Prior Yield", "Ave. Yield" (shown as *XX.X), "Prelim Yld" / "Prelim Yield" / "Preliminary Yield", "T Yield", "Yld Floor", or "Yld Ind". Only "Apprv Yld" / "Appr Yld" / "Approved Yield".
     * **PRELIM YIELD vs APPROVED YIELD** — these are two different numbers in the same summary box. Prelim Yield is the simple historical mean (e.g. 191 bu for Howlett Livingston CORN). Approved Yield is RMA's final number after Trend Adjustment / Yield Cup / T-Yield substitution (e.g. 196 bu for the same unit). When both are printed, EXTRACT APPROVED, NEVER PRELIM. The difference for corn with TA elected is typically 3–15 bu — small enough that visually they look similar, which is exactly why the wrong one gets extracted by accident.
     * If a block has both "Adj. Yield" and "Apprv Yld" side by side, take "Apprv Yld". If only "Adj. Yield" is present and "Apprv Yld" is blank/missing, use "Adj. Yield" as the fallback — but NEVER fall back to Prelim Yield.
     * If multiple unit blocks exist for same county+crop+TYPE+PRACTICE (e.g. unit 0001 and unit 0003 both Erie CORN/GSG/Non-Irrigated):
       - Output ONE ROW PER UNIT — do NOT average. Each unit gets its own row with its own "Apprv Yld" value.
       - Include the most recent year's acres for that unit in an "acres" field (from the production history table — the most recent non-zero acres row).
       - Example: Erie CORN/GSG has unit 0001 (Apprv Yld=138, 2025 acres=200) and unit 0003 (Apprv Yld=152, 2025 acres=50) → output TWO rows: {county:"ERIE", crop:"CORN", type:"GSG", practice:"Non-Irrigated", approvedYield:138, acres:200} and {county:"ERIE", crop:"CORN", type:"GSG", practice:"Non-Irrigated", approvedYield:152, acres:50}. The app will compute a liability-weighted average.
       - Set unitCount = 1 for every row (the app computes the final unitCount after grouping).
     * Example (Anderson Farms page 3): Left block = County:Livingston, Type:GSG, Apprv Yld:180 → {county:LIVINGSTON, type:GSG, approvedYield:180}. Right block = County:Livingston, Type:GSS, Apprv Yld blank, Ave. Yield:*16.8 → {county:LIVINGSTON, type:GSS, approvedYield:16.8}. Both rows required.
     * Example (organic farm): Left block = County:Monroe, Type:GSG, Practice:NON IRR, Apprv Yld:200 → {county:"MONROE", crop:"CORN", type:"GSG", practice:"Non-Irrigated", approvedYield:200}. Right block = County:Monroe, Type:GSG, Practice:NON-IRROC, Apprv Yld:160 → {county:"MONROE", crop:"CORN", type:"GSG", practice:"Organic", approvedYield:160}. BOTH rows required — same county+crop+type but different practice means two separate insured units.
     * YIELD RANGE SANITY CHECK — before finalizing, verify each yield makes sense for its type:
       - GSG (corn grain): valid range 50–300 bu/ac. If you extracted a number < 20, you likely misread the Type field — re-check the block header.
       - GSS (corn silage): valid range 5–40 ton/ac. If you extracted a number > 50, you misread bu as ton — re-check the block header.
       - SOYBEANS: valid range 20–80 bu/ac
       - WHEAT: valid range 20–120 bu/ac
       - POTATOES: valid range 100–600 cwt/ac (hundredweight per acre). If you see a value outside this range, re-check.
     * SELF-VERIFICATION: After extracting all yields:
       1. For every county with a GSS row: "Did I also extract a GSG row for this county?" — if yes in doc, must appear.
       2. For every county+crop+type with a NON-IRROC block: "Did I output a separate row with practice=\"Organic\" for this county+crop+type?" — if yes in doc, must appear.
       3. For every county+crop+type with a NON-IRR block: "Did I output a row with practice=\"Non-Irrigated\"?" — must appear.
       4. Check: no GSG yield < 20, no GSS yield > 50.
       FAILURE MODE TO AVOID: Do NOT collapse Monroe CORN/GSG/NON-IRR and Monroe CORN/GSG/NON-IRROC into one row. They are two separate rows with different approved yields.
   - documentType: "APH_DATABASE"
   - ADDITIONAL FIELD productionReportUnits: Extract from Page 2 "Production Reporting Unit Summary" table.
     This table header says "(YEAR Production)" e.g. "(2025 Production)". Extract the year.
     For each data row in the table:
       * reportedCropYear: the year from the table header (e.g. 2025)
       * county: convert the county FIPS code in "Cty" column to county name:
         003=ALLEGANY, 013=CHAUTAUQUA, 015=CHEMUNG, 029=ERIE, 037=GENESEE, 049=LIVINGSTON,
         051=LIVINGSTON, 055=MONROE, 063=NIAGARA, 065=ONEIDA, 067=ONONDAGA, 069=ONTARIO,
         073=ORLEANS, 099=SENECA, 101=STEUBEN, 107=TIOGA, 109=TOMPKINS, 117=WAYNE, 121=WYOMING
         (use the county name shown in the document header if code not listed above)
       * crop: uppercase — "CORN", "SOYBEANS", "WHEAT", "DRY BEANS" etc (from Crop Plan column)
       * type: "GSG" or "GSS" or "NTS" etc from Practice/Type column
       * acres: the Acres number (decimal)
       * yieldPerAcre: the Yield Per Acre number (decimal, 0 if blank/PP)
       * totalProduction: the numeric Total Production value (before descriptor letter)
       * productionRecordType: the letter suffix on Total Production (A=sold, M=claim, N=appraisal)
     Include ALL rows including GSS (silage) and zero-production rows — app handles filtering.
     Example output:
     "productionReportUnits": [
       {"reportedCropYear":2025,"county":"ALLEGANY","crop":"CORN","type":"GSG","acres":5.27,"yieldPerAcre":145.0,"totalProduction":766.0,"productionRecordType":"A"},
       {"reportedCropYear":2025,"county":"LIVINGSTON","crop":"CORN","type":"GSG","acres":94.07,"yieldPerAcre":163.0,"totalProduction":15328.0,"productionRecordType":"A"},
       {"reportedCropYear":2025,"county":"STEUBEN","crop":"CORN","type":"GSG","acres":365.83,"yieldPerAcre":105.0,"totalProduction":38232.0,"productionRecordType":"A"}
     ]
     If no Production Reporting Unit Summary page exists, return "productionReportUnits": []

3b. Rain & Hail "MPCI Acreage and Production Reporting Form" (RH-5200 / RH-5204):
   - This is a combined application + APH production reporting form from Ace Property and Casualty / Rain and Hail
   - Header (page 1): insured name, policy number (e.g. MP-0808656), crop year (e.g. 2026), state
   - Page 1 coverage table: lists County, Name of Crop, Crop Year, Plan of Insurance (e.g. RP, ECO-RP), Coverage Level (e.g. 0.75 = 75%), Practice (A = Non-Irrigated)
     * Use these rows to get plan and coverageLevel for each county+crop combination
     * Only use the primary RP rows (ignore ECO-RP rows which are supplemental coverage)
   - Page 3 (RH-5204 Acreage and Production Reporting Form): one block per county+crop unit
     * Each block header shows: County code+name (e.g. "51 LIVINGSTON"), crop (e.g. "RP-CORN" → crop=CORN), Practice (NON IRR or NON-IRROC), Type (GSG, NTS, etc.)
     * PRACTICE EXTRACTION (CRITICAL): Read the Practice field in EVERY block header.
       - "NON IRR" → practice="Non-Irrigated" (conventional)
       - "NON-IRROC" → practice="Organic" (certified organic)
       - A farm may have BOTH conventional and organic units for the same county+crop+type — these are separate insured units with different approved yields and MUST be output as separate rows.
       - Example: Genesee CORN/GSG with unit 1.01 Practice=NON IRR (Appr Yld 164) AND unit 1.02 Practice=NON-IRROC (Appr Yld 142) → TWO rows: {county:"GENESEE", crop:"CORN", type:"GSG", practice:"Non-Irrigated", approvedYield:164} and {county:"GENESEE", crop:"CORN", type:"GSG", practice:"Organic", approvedYield:142}
     * Year rows: each row is "YY  total_production  acres  yield  descriptor" — the yield column is the per-acre yield for that year
     * Bottom summary section LAYOUT (R&H RH-5204 form, ALWAYS 4-column for TA-elected policies):
       The summary box has TWO label-value column pairs, like this:

          Total Yield  : <sumProd>    Prior Yield : 0
          ÷ Years      : <n>          Ave Yield   : <prelim>
          = Prelim Yld : <prelim>     Adj Yield   : <prelim>
                                      Rate Yield  : <prelim>
          T-Yield      : <tyield>     Appr Yield  : <approved>   ← BOTTOM-RIGHT CELL

       The bottom-right cell labeled "Appr Yield" / "Apprv Yld" / "Approved Yield"
       is THE ONLY field to extract as approvedYield. Read the LABEL TEXT, not the
       position. The cell's value is on the same horizontal line as its label.

     * NUMERIC FINGERPRINT — use this to verify the right cell:
       In a TA-elected policy (look for "OPTIONS:TA" or "Trend Ylds:" in the
       block's Remarks/Other section), the box contains MULTIPLE identical
       values (Prelim/Ave/Adj/Rate Yield are all the simple mean of the year
       column) and ONE DIFFERENT, HIGHER value (Appr Yield). Example pattern:
         Prelim=191, Ave=191, Adj=191, Rate=191, T=179, Appr=196.
       FIVE numbers around 191 and ONE distinct number around 196. The distinct
       higher number is Appr Yield — extract THAT. Never extract the repeated
       value just because it appears most often.

       If the policy did NOT elect TA (no "OPTIONS:TA" line), Prelim and Appr
       may be identical. That is fine — extract the Appr Yield value anyway,
       which happens to equal Prelim in that case.

     * WORKED EXAMPLE — Howlett Farms 2026, Livingston CORN GSG block:
       Block content (verbatim):
         51 LIVINGSTON   RP-CORN   NON IRR   GSG
         16 ... 141 TA
         17 A ... 192 TA
         ... (8 more year rows)
         25 A 99155 514.6 193 TA
         FSN: 3270   2026
         Total Yield 1910.0   Prior Yield 0
         ÷ Years 10           Ave Yield 191
         = Prelim Yield 191   Adj Yield 191
                              Rate Yield 191
         T-Yield 179          Appr Yield 196      ← EXTRACT THIS: approvedYield=196
         OPTIONS:TA
         Non-Trend App Yld 191.0
       CORRECT extraction: {county:"LIVINGSTON", crop:"CORN", type:"GSG",
       practice:"Non-Irrigated", approvedYield:196}
       WRONG (DO NOT DO THIS): approvedYield=191. That is Prelim Yield. It
       appears 4 times in the summary, which is misleading — count of
       appearances is NOT how you identify the right field. The LABEL is.

     * ADDITIONAL FIELDS to NEVER extract as approvedYield:
       - "Total Yield" (sum of year column — an input to the calc)
       - "Prior Yield" (typically 0 for established units)
       - "÷ Years" (count, an input to the calc)
       - "Ave Yield" (= Total ÷ Years, mathematically equal to Prelim)
       - "= Prelim Yield" / "Preliminary Yield" / "Prelim Yld" — the simple
         historical mean BEFORE TA / Yield Cup / T-Yield substitution
       - "Adj Yield" — adjusted yield, intermediate
       - "Rate Yield" / "Rate Yld" — used for premium-rate calc
       - "T-Yield" — county T-yield used as a floor
       - "Non-Trend App Yld" in Remarks/Other — informational only

     * SELF-CHECK: After picking each unit's yield, verify TWO things:
       1. The label adjacent to the number contains "Apprv", "Appr", or "Approved" — NOT "Prelim", "Ave", "Adj", "Rate", "T-", "Total", or "Non-Trend".
       2. If the block has "OPTIONS:TA" in its Remarks, the value you picked
          MUST be DIFFERENT FROM (and ≥) the Prelim Yield value. If they're
          equal in a TA block, you grabbed the wrong cell — re-read and find
          the bottom-right "Appr Yield" cell.

     * FALLBACK CHAIN: Apprv Yld → (if blank) Adj. Yield → never fall back to
       Prelim Yield, T-Yield, Rate Yield, or Ave Yield.

     * AGENT NAME GUARDRAIL — R&H RH-5200 page 1 has an "Agency/Agent
       Information" block. The AGENCY name is real (e.g. "SPARTAN INS AGENCY
       LLC"). The AGENT name on the document is OFTEN BLANK — only the agency
       code is filled (e.g. "5727-00"). DO NOT mistake "SA: <name>" for the
       agent. "SA:" stands for Signature Authorization — a corporate officer
       authorized to sign on behalf of the insured, NOT the insurance agent.
       Example: Howlett Farms 2026 page 1 shows "SA: MICHAEL HOWLETT" — that
       is Howlett's corporate signer (an SBI holder shown on page 2), not the
       agent. The agent_name field for an R&H APH document should be NULL
       unless an explicit "Licensed Agent's Printed Name" field on page 4 is
       filled in. Leave it null otherwise — never default to SA, agency
       contact, or the insured's own name.
     * CRITICAL: Do NOT extract acres from an APH document. Acres in APH blocks are historical production acres, not 2026 insured planted acres. Set acres=null for all rows from this document.
     * CRITICAL: Return ONE ROW PER UNIT — do NOT average in your head. If Genesee CORN has 5 unit blocks (0001-0001-000 through 0001-0005-000), output 5 separate rows, each with that unit's exact "Apprv Yld" value. The app will average them in code.
     * Set unitCount = 1 for every row (the app computes the final unitCount after averaging).
     * Example: Genesee CORN has units 0001-0001-000 (Practice NON IRR, Apprv Yld 184), 0001-0002-000 (Practice NON-IRROC, Apprv Yld 142), 0001-0003-000 (Practice NON IRR, Apprv Yld 168) → output THREE rows: {county:GENESEE, crop:CORN, type:GSG, practice:"Non-Irrigated", approvedYield:184}, {county:GENESEE, crop:CORN, type:GSG, practice:"Organic", approvedYield:142}, {county:GENESEE, crop:CORN, type:GSG, practice:"Non-Irrigated", approvedYield:168}. The app will average per practice: conventional avg=(184+168)/2=176 unitCount=2, organic=142 unitCount=1.
     * Single unit (most farms): still output one row with unitCount=1.
     * CRITICAL: GSG and GSS are DIFFERENT TYPES — each gets its own row. A farm with both CORN/GSG and CORN/GSS blocks in the same county must produce TWO separate approvedYields rows for that county.
     * YIELD RANGE SANITY CHECK — before finalizing, verify each yield makes sense for its type:
       - GSG (corn grain): valid range 50–300 bu/ac. If you see a number < 20, you likely read a silage yield — re-check.
       - GSS (corn silage): valid range 5–40 ton/ac. If you see a number > 50, you read bu not ton — re-check.
       - SOYBEANS/NTS: valid range 20–80 bu/ac
       - WHEAT: valid range 20–120 bu/ac
       - POTATOES: valid range 100–600 cwt/ac (hundredweight). If you see a value outside this, re-check.
     * SELF-VERIFICATION: After extracting all yields, for every county that has a GSS row ask yourself: "Did I also extract a GSG row for this county?" If the farm has a GSG block anywhere in the document for that county, it must appear in your output.
     * PRACTICE SELF-VERIFICATION: After extracting all yields:
       1. For every county+crop+type: "Did I see both a NON IRR and a NON-IRROC block?" If yes, I must have TWO separate output rows — one with practice="Non-Irrigated" and one with practice="Organic". If I only output one, go back and find the missing block.
       2. Count total blocks in the document and count total output rows — they must match. If they don't, I missed a block.
   - documentType: "APH_DATABASE"
   - aip: "RAIN_AND_HAIL"
   - IMPORTANT: This form looks like an application form but IS the APH database report — the production history table IS the APH data
   - ADDITIONAL FIELD productionReportUnits: From each unit block on the production reporting page, find the MOST RECENT year row (highest 2-digit year number, e.g. "25" = 2025) and extract it.
     Each year row format: "YY [descriptor] totalProduction acres yield recordType [L]"
     Example row: "25 H 128611 634.6 203 TA L" → year=2025, totalProduction=128611, acres=634.6, yieldPerAcre=203, productionRecordType=TA
     Example row: "25 A 18220 289.2 63 A" → year=2025, totalProduction=18220, acres=289.2, yieldPerAcre=63, productionRecordType=A
     For each unit block extract:
       * reportedCropYear: 2000 + the 2-digit year (e.g. 25 → 2025)
       * county: from the block header county name (e.g. "51 LIVINGSTON" → "LIVINGSTON")
       * crop: from the block header crop name — "RP-CORN"→"CORN", "RP-SOYBEANS"→"SOYBEANS", "RP-WHEAT"→"WHEAT"
       * type: from the block header type field — "GSG", "GSS", "NTS", etc.
       * acres: the acres value from the most recent year row (3rd column)
       * yieldPerAcre: the yield value from the most recent year row (4th column)
       * totalProduction: the production value from the most recent year row (2nd column)
       * productionRecordType: the record type code (A=actual, TA=trend adjusted, M=claim, N=appraisal, etc.)
     IMPORTANT: If a block is marked "Uninsurable" or has a zero production year (Z suffix), still include the row with yieldPerAcre=0.
     If multiple unit blocks exist for same county+crop+type, output ONE row per block (not averaged — app handles it).
     Output as: "productionReportUnits": [
       {"reportedCropYear":2025,"county":"LIVINGSTON","crop":"CORN","type":"GSG","acres":634.6,"yieldPerAcre":203,"totalProduction":128611,"productionRecordType":"TA"},
       {"reportedCropYear":2025,"county":"LIVINGSTON","crop":"SOYBEANS","type":"NTS","acres":289.2,"yieldPerAcre":63,"totalProduction":18220,"productionRecordType":"A"}
     ]
     If no production year rows found, return "productionReportUnits": []

4. "Schedule of Insurance" (SOI) - Crop — TWO FORMATS:

4a. NAU Country "Schedule of Insurance" (MPCI):
   - Header: "SCHEDULE OF INSURANCE / Acreage Report & Production Report / Multiple Peril Crop Insurance"
   - STEP 0 — Identify the PRIMARY CROP of this SOI document:
     * Look at the per-unit detail pages (pages after the Summary of Coverage). The crop that has its own unit detail pages with "Appd. Yld/Rev." values IS the primary crop.
     * Fall-planted crops (WHEAT, BARLEY, TRITICALE, RYE) will have unit detail pages in a fall wheat SOI.
     * Spring-planted crops (CORN, SOYBEANS, DRY BEANS, PROCESSING BEANS) will NOT have unit detail pages in a fall wheat SOI — they appear only as rows in the Summary of Coverage table with carry-forward acres from the prior year's acreage report.
     * CRITICAL RULE: Only extract approvedYields rows for crops that have their own unit detail pages with an "Appd. Yld/Rev." value. If a crop appears ONLY in the Summary of Coverage table but has NO unit detail pages, set acres=null and approvedYield=null for that crop row — DO NOT extract its acres from the Summary of Coverage table.
     * Why: The "Insured Acres" column in the Summary of Coverage for spring crops on a fall wheat SOI shows prior-year carry-forward acres — these are NOT the actual 2026 insured acres and must not be imported as such.

   - STEP 1 — Read the "Summary of Coverage" table (page 3, labeled columns: County, Des., Add. Cty, Crop, Practice/Type, Plan, Coverage Level, % of Price Election, Insured Acres, Options):
     * Each row = one county+crop+plan combination
     * COUNTY is in the first column — each row has its OWN county. Do NOT assume all rows share the same county.
     * INSURED ACRES is the column labeled "Insured Acres" — read each row independently
     * COVERAGE LEVEL is the percentage in the "Coverage Level" column (e.g. "80%" → 0.80, "75%" → 0.75)
     * IMPORTANT: ECO-RP rows show a range like "86% - 95%" — this is NOT the base coverage level. Skip ECO-RP rows for approvedYields.
     * Only use RP rows (not ECO-RP, not SCO) for coverageLevel and acres
     * A county may have 0 insured acres if that county was listed but no acreage was reported there — record 0 or null, do NOT copy acres from another county
     * CRITICAL: For any crop that has no unit detail pages (see STEP 0), set acres=null regardless of what the Summary of Coverage table shows
   - STEP 2 — Read per-unit detail pages for approved yield:
     * Each detail page shows: county (top right), legal number, unit number, crop, plan, Acres, Appd. Yld/Rev., Guarantee/Acre, Price, Liability
     * "Appd. Yld/Rev." = approved yield in bu/ac — this is approvedYield. It is a YIELD NUMBER (e.g. 76.0, 72.0) NOT a percentage.
     * CRITICAL: Do NOT confuse the ECO-RP coverage range "86% - 95%" in the Summary of Coverage table with the approved yield. Those are coverage percentages, not yields. The approved yield comes ONLY from the "Appd. Yld/Rev." column on the per-unit detail pages.
     * CRITICAL: Different counties on the same policy will have DIFFERENT approved yields — read each county's detail pages separately (e.g. Livingston may be 76 bu, Monroe may be 72 bu — do NOT apply one county's yield to the other).
     * Sum insured acres for each county from the unit detail pages (RP lines only, skip ECO-RP lines which have no separate acres)
   - STEP 3 — Build one approvedYields entry per unique county+crop combination:
     * county: from the Summary of Coverage row (e.g. "Genesee", "Monroe", "Livingston")
     * acres: sum of all RP unit insured acres FOR THAT SPECIFIC COUNTY ONLY (NOT the policy total)
       - CRITICAL: The policy total acres (shown on page 1 or the premium summary) is the SUM across all counties — NEVER use this number for a single county's acres.
       - Example: if the policy total is 482.71 ac and Livingston=367.10, Monroe=115.61, then Livingston gets 367.10 and Monroe gets 115.61. Do NOT put 482.71 on either county.
       - If Summary of Coverage shows 0 acres for a county, set acres to 0 (farmer has coverage there but no acres reported yet)
       - NEVER copy acres from one county to another
     * approvedYield: from per-unit detail pages for that county (the "Appd. Yld/Rev." column value)
     * coverageLevel: from the RP row for that county in the Summary of Coverage (e.g. 0.80 for 80%)
     * plan: "RP"
   - STEP 4 — Capture SCO and ECO endorsements from the Summary of Coverage table:
     * Look for rows with Plan = "SCO-RP" or "ECO-RP" in the Summary of Coverage
     * For each SCO/ECO row, extract: county, crop, plan ("SCO-RP" or "ECO-RP"), acres (same as base RP row), coverageLevel
     * SCO coverage level: typically shown as a single percent (e.g. 86% → 0.86)
     * ECO coverage level: shown as a range like "86% - 95%" — use the UPPER value (e.g. 0.95)
     * type and practice: same as the corresponding base RP row for that county+crop
     * These go in scoEcoEndorsements array (NOT approvedYields)
   - STEP 5 — Extract premium from the "MPCI Premium Summary" page:
     * This page has columns: County | Crop | Plan | Planted Acres | Insured Acres | Premium Liability | Marketable Guarantee | Gross Premium | Amount of Subsidy Paid by RMA | A&O Subsidy | Insured Premium | Amount of Admin Fees | Billing Date
     * For each RP row (not ECO-RP): extract county, crop, totalPremium ("Gross Premium" column), subsidy ("Amount of Subsidy Paid by RMA" column), netPremium ("Insured Premium" column), billingDate
     * Match each row to its approvedYields entry by county+crop and set totalPremium, subsidy, netPremium, billingDate on that entry
     * ECO-RP rows: add their gross premium to the matching county+crop approvedYields entry's totalPremium (ECO premium is part of the farmer's total gross premium for that crop)
     * If the premium page is missing or unclear, leave these fields null — do not guess
   - STEP 5b — ALSO copy that page's "Policy Total" row into premiumTotals, VERBATIM.
     * Copy it; never add the rows up yourself. Its whole purpose is to disagree with your
       row-by-row extraction when you have missed a row, which is the one error a per-row
       check can never see — a 2026-08-06 load quietly dropped rows ($270,374 against a
       stated $305,771) and only this row revealed it.
     * grossPremium = "Gross Premium" total, subsidy = the subsidy total, netPremium =
       "Insured Premium" total, liability = "Premium Liability" total, insuredAcres =
       "Insured Acres" total. Null anything the document does not state.
     * Do NOT fold Admin Fees into netPremium — the fee sits outside gross = subsidy + net.
   - STEP 5c — netPremium ("Insured Premium") is REQUIRED on every row carrying a gross.
     It is what the grower is BILLED and it cannot be derived: the subsidy rate depends on
     coverage level AND unit structure. If a row shows a gross, find its Insured Premium.
     The one exception is CAT, where subsidy equals gross and the producer share is nil.
   - STEP 5d — TYPE is REQUIRED on CORN, ONIONS, GRAPES, APPLES, CABBAGE, DRY BEANS,
     PROCESSING BEANS and BARLEY. RMA prices these PER TYPE, so an untyped row can be two
     different products blended into a per-acre number that is neither: corn grain (bu/ac)
     against silage (TON/ac), or onion Reds at $25.80 against Yellows at $15.60 — same CWT
     unit, different money. If one county+crop shows more than one type, emit ONE ROW PER
     TYPE with that type's own acres, liability and premium. Never merge them.
   - ALWAYS populate approvedYields for SOI_CROP documents — including WHEAT
   - documentType: "SOI_CROP", aip: "NAU"

   NOTE — NAU Compact "Schedule Of Insurance & Production Report" (2026 format, NY-944-XXXXXXX-26):
   This newer NAU format has the same documentType=SOI_CROP but differs in layout:
   - Page 1 has a "Summary of Coverage" table with all counties/crops in one place (County | Crop | Practice/Type | Plan | Coverage Level | Insured Acres)
   - Per-unit detail pages (pages 2+) list: Line#, Unit#, county code (e.g. 055 Monroe), Crop, Plan, Acres, Appd. Yld/Rev., Liability, Gross Premium, Premium Subsidy, Insured Premium
   - MPCI Premium Summary page has columns: Crop | Plan | County | Planted Acres | Insured Acres | Premium Liability | Marketable Guarantee | Gross Premium | Premium Subsidy | A&O Subsidy | Insured Premium | Admin Fees | Balance | Billing Date
   - Insured Acres in Summary of Coverage = authoritative for RP rows. BLANK = no acres reported yet → set acres=null
   - Appd. Yld/Rev. on detail pages = approved yield in bu/ac (e.g. 67 = 67 bu/ac wheat, 72 = 72 bu/ac triticale) — NOT a percentage
   - Counties with all-zero premium rows in MPCI summary = coverage elected but not yet reported — still create approvedYields row with acres=null
   EXAMPLE (MICHAEL J MEISENZAHL NY-944-6062818-26):
     Summary of Coverage: Livingston WHEAT RP 75% [blank acres] → acres=null; Monroe WHEAT RP 75% 30.29ac → acres=30.29; Ontario WHEAT RP 75% [blank] → acres=null
     Detail page: Monroe WHEAT RP line2 → 30.29ac, Appd Yld=67bu; Monroe TRICL APH line5 → 36.00ac, Appd Yld=72bu
     MPCI Summary: Monroe WHEAT RP → totalPremium=1597, subsidy=958, netPremium=639; Livingston WHEAT RP → all zero
     approvedYields: Monroe WHEAT RP acres=30.29 yield=67; Livingston WHEAT RP acres=null yield=null; Ontario WHEAT RP acres=null yield=null; Monroe TRITICALE APH acres=36.0 yield=72
     scoEcoEndorsements: Monroe SCO-RP 0.86 30.29ac; Monroe ECO-RP 0.95 30.29ac; Ontario SCO-RP 0.86 null; Ontario ECO-RP 0.95 null


4b. Rain & Hail / ACE "MAP BASED PRODUCTION REPORTING AND SUMMARY OF INFORMATION":
   - Header: "ACE PROPERTY AND CASUALTY INSURANCE COMPANY / Crop Insurance Serviced By RAIN AND HAIL L.L.C."
   - Sub-header: "MAP BASED PRODUCTION REPORTING AND SUMMARY OF INFORMATION"
   - Policy number format: MP-XXXXXXX (e.g. MP-0865595)
   - This document has one page per field group (tract). Each page shows:
     * Field group header: "Map: Field Group: (N) F:XXXX T:XXXX" and county line "COUNTY, NY MAP KEY..."
     * Field table: rows like "1.0 WHEAT 5939-6502-5  154.69  155.00  PL  09/19/25"
       - Columns are: Yield#, Crop, Farm-Tract-Field, Field Acres, Insured Acres, Type (PL=planted), Plant Date
       - The INSURED ACRES is the SECOND number (e.g. 155.00, NOT 154.69 which is field acres)
       - Sum all insured acres across all unique Farm-Tract-Field entries (deduplicate — same tract may appear twice in the doc as separate SOI/production copies)
     * Revenue Protection Summary table repeats on every page — it is the POLICY-LEVEL summary, not per-field:
       - EU line: "WHEAT W EU 1.0000 1.000 4822 5384 79.0 BU $364.03 458.8 $167,017.88 $5.76 $167,018 $6,399"
       - The EU line columns are: Crop, W, Unit type, Interest, Coverage (4822=48.22%? No — read as: approved yield area=4822, coverage=5384), Description, Approved Yield (79.0 BU), Guarantee/Acre ($364.03), TOTAL INSURED ACRES (458.8), Revenue Guarantee ($167,017.88), Price ($5.76), Liability, Premium
       - approvedYield = the BU value on the EU line (e.g. 79.0)
       - acres = total insured acres from EU line (e.g. 458.8) — use this as the policy total acres
       - coverageLevel: derive from guarantee/acre ÷ (approvedYield × price) = 364.03 ÷ (79.0 × 5.76) = 0.80 → 0.80
       - OR look for coverage level % shown elsewhere (e.g. "79.00 BU" with "Coverage" column)
   - SCO and ECO lines (BU type with remarks "SCO FOR LINE" or "ECO FOR LINE") are supplemental — skip for approvedYields
   - If SCO or ECO lines are present, capture them in scoEcoEndorsements:
     * plan: "SCO-RP" for SCO lines, "ECO-RP" for ECO lines
     * county, crop, type, practice: same as the base RP row
     * acres: same as base RP acres
     * coverageLevel: read from the coverage level shown on that line
   - county: all fields are same county (shown on each page, e.g. "ORLEANS")
   - plan: "RP" (Revenue Protection)
   - Extract ONE approvedYields entry per county+crop combination using the EU summary line values
   - documentType: "SOI_CROP", aip: "RAIN_AND_HAIL"

4c. Rain & Hail / ACE "2026 SUMMARY OF INFORMATION" — Compact Tabular SOI (RH-1880, no maps):
   - Header: "ACE PROPERTY AND CASUALTY INSURANCE COMPANY / Crop Insurance Serviced By RAIN AND HAIL L.L.C. / 2026 SUMMARY OF INFORMATION"
   - This is the NON-map version (no "MAP BASED" subtitle). Policy number format: MP-XXXXXXX.
   - Page 1 has a flat COVERAGE table with columns: COUNTY | PLAN | CROP | TYPE CLASS,ETC | OPTION | LEVEL | PRICE | FINAL PLANT DATE | ACRES | LIABILITY | PREMIUM | SUBSIDY | NET PREMIUM | CREDITS | FEES | INTEREST | BALANCE | BILLING DATE
   - Below the COVERAGE table are two separate detail sections:
     * "2026 MULTIPLE PERIL CROP INSURANCE SUMMARY OF INFORMATION" — for APH/YA plan crops (e.g. TRITICALE APH)
     * "2026 REVENUE PROTECTION SUMMARY OF INFORMATION" — for RP plan crops (e.g. WHEAT RP)
   - Pages 2+ continue the "2026 REVENUE PROTECTION SUMMARY OF INFORMATION" table with remaining line items

   STEP 1 — Read the COVERAGE table on page 1 to get acres and plan per county+crop:
     * Each data row has: COUNTY, PLAN, CROP, TYPE CLASS (e.g. "A"), OPTION string, LEVEL (coverage % e.g. 75 → 0.75), PRICE (e.g. 5.76), FINAL PLANT DATE, ACRES, LIABILITY, PREMIUM, SUBSIDY, NET PREMIUM, BALANCE, BILLING DATE
     * ECO-RP rows are indented under their parent RP row — they share the same COUNTY, CROP, ACRES. Their LEVEL shows "95" and "100%" — skip for approvedYields but capture in scoEcoEndorsements.
     * APH rows (like TRITICALE APH) have PLAN=APH and a TYPE YA/C. The LEVEL for APH is the coverage level (e.g. 50 → 0.50).
     * Net premium for each RP row = the NET PREMIUM column (after subsidy). The BALANCE column = net premium + fees.
     * Extract netPremium from the NET PREMIUM column for each base RP/APH row.

   STEP 2 — Read the per-line detail sections for approved yield and exact acres:
     * Each entry begins with a "Line#" header: e.g. "1.0 WHEAT" or "2.0 TRICL"
     * Below that is a "YLD#" row: e.g. "YLD# NON IRR EU 1.0000 188 1.000 SCHUYLER 89.0 BU $384.77 46.1 09/10/2025 ..."
       - Format: YLD# [PRACTICE_CODE] [UNIT_TYPE] [UNIT_NUM] [FARM] [INTEREST] [COUNTY] [APH_YIELD] BU [ACRE_COVERAGE] [ACRES] [PLANT_DATE] [REVENUE_GUARANTEE] [PROD_GUARANTEE] BU [PRICE] [LIABILITY] [PREMIUM]
       - Practice code: NON IRR → practice="Non-Irrigated", IRR → practice="Irrigated"
       - Unit type: EU = Enterprise Unit, BU = Basic Unit
       - approvedYield = the number before "BU" in the yield position (e.g. "89.0 BU" → 89.0; "99.0 BU" → 99.0)
       - acres = the number in the ACRES column (e.g. 46.1, 228.7, 1066.2, 29.3, 28.1)
       - county = the county name in that row (e.g. SCHUYLER, SENECA, TOMPKINS)
     * ECO-RP lines are identified by Remarks containing "ECO FOR LINE:" — skip these for approvedYields.
     * *REDUCED* / *LATE* lines for a crop already covered by a primary line in the same county — SKIP these (they are late-planted acres with reduced guarantee, already counted in the COVERAGE table acres).
     * APH plan crops (TRITICALE, etc.): extract from the MULTIPLE PERIL section. approvedYield = value before BU (e.g. 99.0). plan = "APH".

   STEP 3 — Aggregate acres by county+crop for RP lines:
     * A county+crop may have MULTIPLE non-ECO, non-REDUCED lines (e.g. Seneca WHEAT has lines 3.0 and 4.0 *REDUCED*).
     * SUM the acres from all non-REDUCED, non-ECO lines for each county+crop. Use line 3.0 only (not 4.0 *REDUCED*) for Seneca.
     * For large farms with both EU and late *REDUCED* entries, the COVERAGE table ACRES column is the authoritative total — use it if summing detail lines gives a different total.
     * PREFERRED: use the COVERAGE table ACRES column as the definitive acres for each county+crop RP row.
     * approvedYield: use the yield from the primary (non-REDUCED) EU line. If both lines have the same approvedYield, that confirms it.

   STEP 4 — ECO-RP endorsements:
     * Any ECO-RP indented row in the COVERAGE table → add to scoEcoEndorsements:
       { county, crop, plan:"ECO-RP", coverageLevel:0.95, acres: same as parent RP row, type: same as parent, practice: "Non-Irrigated" }

   STEP 5 — APH crops (non-RP):
     * TRITICALE APH: read from MULTIPLE PERIL section. approvedYield=99.0 (or as shown), acres from COVERAGE table, coverageLevel=0.50 (or as shown), plan="APH".
     * Add to approvedYields just like RP crops.

   STEP 6 — Premium data:
     * For each RP row extract: totalPremium (PREMIUM column), subsidy (SUBSIDY column), netPremium (NET PREMIUM column), billingDate (BILLING DATE column).
     * Store these on the approvedYields entry as: totalPremium, subsidy, netPremium, billingDate.

   KEY EXTRACTION RULES for this format:
     * approvedYield comes from the detail lines (YLD# rows), NOT from the COVERAGE table (which has no yield column).
     * acres comes from the COVERAGE table ACRES column (authoritative) OR the detail line acres (they should match for non-REDUCED lines).
     * Do NOT use acres from *REDUCED* or ECO-RP lines.
     * County names: use full names (SCHUYLER, SENECA, TOMPKINS) — normalize to Title Case matching other counties.
     * Agent: the agent name listed is the contact person (e.g. MEISENZAHL) — use agency name "SPARTAN INS AGENCY LLC".
     * Insurer: "ACE Property and Casualty / Rain and Hail"
     * aip: "RAIN_AND_HAIL"
     * documentType: "SOI_CROP"

   EXAMPLE from Austic Farm Partners MP-0865834:
     COVERAGE table shows:
       SCHUYLER RP WHEAT 75% 46.10 ac  → approvedYields entry: {county:"Schuyler", crop:"WHEAT", plan:"RP", coverageLevel:0.75, acres:46.10, approvedYield:89.0 (from line 1.0)}
       SCHUYLER ECO-RP 95% 46.10 ac    → scoEcoEndorsements: {county:"Schuyler", crop:"WHEAT", plan:"ECO-RP", coverageLevel:0.95, acres:46.10}
       SENECA APH TRITICALE 50% 228.70 ac → approvedYields: {county:"Seneca", crop:"TRITICALE", plan:"APH", coverageLevel:0.50, acres:228.70, approvedYield:99.0}
       SENECA RP WHEAT 75% 1095.50 ac  → approvedYields: {county:"Seneca", crop:"WHEAT", plan:"RP", coverageLevel:0.75, acres:1095.50, approvedYield:89.0 (line 3.0, ignoring *REDUCED* line 4.0)}
       SENECA ECO-RP 95% 1095.50 ac    → scoEcoEndorsements
       TOMPKINS RP WHEAT 75% 28.10 ac  → approvedYields: {county:"Tompkins", crop:"WHEAT", plan:"RP", coverageLevel:0.75, acres:28.10, approvedYield:83.0 (from line 5.0)}
       TOMPKINS ECO-RP 95% 28.10 ac    → scoEcoEndorsements


5. "Schedule of Insurance" - Dairy Revenue Protection (DRP):
   - Header: insured name, policy number (e.g. NY-944-6071197-26), crop year, agent, agency, insurer
   - Each endorsement row has: End#, a practice type code (e.g. "OCTDECY2Q4" or "JANMARY3Q1"), effective date, end date, Ins Shr, declared milk production (lbs), expected milk per cow, Class III flag, coverage level, projected price, trigger price, expected revenue guarantee, liability, total premium, subsidy, producer premium
   - The quarter MUST be derived from the practice type code OR the end date:
     * Codes containing Q1 or JANMAR or FEBMAR or MARapr = Q1 (Jan-Mar)
     * Codes containing Q2 or APRMAY or MAYJUN or APRJUN = Q2 (Apr-Jun)  
     * Codes containing Q3 or JULAUG or JULSEP or AUGSEP = Q3 (Jul-Sep)
     * Codes containing Q4 or OCTNOV or OCTDEC or NOVDEC = Q4 (Oct-Dec)
     * If end date is Jan-Mar → Q1, Apr-Jun → Q2, Jul-Sep → Q3, Oct-Dec → Q4
     * Format quarter as "Q1 2026", "Q2 2025", etc. using the YEAR from the end date
   - classIII: 1 if the endorsement is Class III pricing, 0 if Class IV pricing
   - declaredMilkProd is in pounds (lbs), typically 500000 to 5000000 per endorsement
   - documentType: "SOI_DRP"

6. "Livestock Risk Protection" (LRP):
   - Header: "2026 LIVESTOCK RISK PROTECTION SUMMARY OF INFORMATION" (R&H/ACE) or similar NAU LRP document
   - documentType: "LRP", aip: "RAIN_AND_HAIL" (or "NAU")
   - Policy number format: MP-XXXXXXX (R&H) or NY-944-... (NAU)

   STEP 1 — Read the COVERAGE table (top section):
     * Columns: COUNTY | PLAN | CROP (e.g. FEEDER CATTLE) | TYPE CLASS (e.g. SW1, SW2, HW1) | OPTION | EFFECTIVE DATE | # OF HEAD | INSURED VALUE | PREMIUM | SUBSIDY | NET PREMIUM | CREDITS | FEES | INTEREST | BALANCE | BILLING DATE
     * Each row = one endorsement. Multiple rows share the same county/crop header.
     * TYPE CLASS codes: SW1=Steers/Heifers <6cwt (weight class 1), SW2=Steers/Heifers 6-9cwt, HW1=Heifers <6cwt, etc.
     * EFFECTIVE DATE = sales date (when the endorsement was purchased)

   STEP 2 — Read the per-endorsement detail section (lower half, "2026 LIVESTOCK RISK PROTECTION SUMMARY OF INFORMATION" table):
     * Columns: Line# | Crop | Interest | County | Endorsement Description | End Date | Endorsement Length | # of Head | Effective Date | Target Weight | Price | Insured Value | Premium
     * Line# format: "1.0 FDR CATTLE / EEMAR26 / 1.000 / YATES" → commodity=Feeder Cattle, endorsement code=EEMAR26, county=YATES
     * The sub-line (e.g. "5.0 SW2") = the type/weight class
     * End Date = when coverage ends (e.g. 03/24/2026)
     * Endorsement Length = duration (e.g. "30 wks", "17 wks")
     * Target Weight = cwt (e.g. 9.00 cwt, 5.00 cwt, 7.50 cwt)
     * Price = coverage price per cwt (e.g. $350.68, $342.79)
     * Premium = net premium (insured's share)

   STEP 3 — Build one lrpEndorsements entry per endorsement row:
     * commodity: "Feeder Cattle" (from FDR CATTLE), "Fed Cattle" (from FED CATTLE), "Unborn Cattle", "Swine", etc.
     * type: the weight class code (e.g. "SW2", "HW1", "SW1") — from the sub-line
     * county: county name (e.g. "Yates")
     * salesDate: EFFECTIVE DATE from coverage table (format YYYY-MM-DD, e.g. "2025-08-26")
     * endDate: End Date from detail section (format YYYY-MM-DD, e.g. "2026-03-24")
     * endorsementLength: weeks as integer (e.g. 30, 17)
     * numberOfHead: # of Head (e.g. 88, 6, 8, 20)
     * targetWeight: Target Weight in cwt (e.g. 9.0, 5.0, 7.5)
     * coveragePrice: Price per cwt (e.g. 350.68, 342.79, 377.06)
     * insuredValue: Insured Value / total value (e.g. 277739, 10284)
     * totalPremium: PREMIUM column from coverage table (gross)
     * subsidy: SUBSIDY column
     * netPremium: NET PREMIUM column (= insured's share)
     * billingDate: BILLING DATE column (format YYYY-MM-DD)

   EXAMPLE from EM Livestock MP-0916551:
     Row 1: county=Yates, commodity=Feeder Cattle, type=SW2, salesDate=2025-08-26, endDate=2026-03-24, endorsementLength=30, numberOfHead=88, targetWeight=9.0, coveragePrice=350.68, insuredValue=277739, totalPremium=21745, subsidy=10178, netPremium=11567, billingDate=2026-05-01
     Row 2: county=Yates, commodity=Feeder Cattle, type=HW1, salesDate=2026-03-09, endDate=2026-07-06, endorsementLength=17, numberOfHead=6, targetWeight=5.0, coveragePrice=342.79, insuredValue=10284, totalPremium=694, subsidy=325, netPremium=369, billingDate=2026-09-01
     Row 3: county=Yates, commodity=Feeder Cattle, type=SW1, salesDate=2026-03-09, endDate=2026-07-06, endorsementLength=17, numberOfHead=8, targetWeight=5.0, coveragePrice=377.06, insuredValue=15082, totalPremium=1017, subsidy=476, netPremium=541, billingDate=2026-09-01
     Row 4: county=Yates, commodity=Feeder Cattle, type=SW2, salesDate=2026-03-09, endDate=2026-07-06, endorsementLength=17, numberOfHead=20, targetWeight=7.5, coveragePrice=342.79, insuredValue=51419, totalPremium=3468, subsidy=1623, netPremium=1845, billingDate=2026-09-01

7. "Pasture Rangeland Forage" (PRF) - Rainfall Index:
   - This is an R&H (Rain and Hail / Ace Property and Casualty) or NAU Country PRF policy
   - Header: insured name, policy number (e.g. MP-0924528 or NY-944-6063006-26), crop year, AIP
   - MULTI-COUNTY SUPPORT: A single PRF SOI often covers MULTIPLE counties and MULTIPLE types (Haying + Grazing).
     The COVERAGE summary table at the top will have SEPARATE ROWS for each county/type combination.
     Example (R&H format - Har-Go Farms):
       GENESEE  PRF RI  GRAZNG  A 85 150%  166.79  14,312   1,476    922    554   ...
       GENESEE  PRF RI  HAYING  A 85 150%  210.86  120,176  12,410  7,759  4,651  ...
       WYOMING  PRF RI          A 85 150%   59.09   33,678   3,926  2,455  1,471  ...
                                           436.74 $168,166 $17,812 $11,136 $6,676
     Example (NAU Country format - Chase Farm Partnership):
       051 - Livingston  Pasture,Rangeland,Forage  HAYING  Rainfall Index  85%  150%  (blank)
       055 - Monroe      Pasture,Rangeland,Forage  HAYING  Rainfall Index  85%  150%  233.11
     NOTE: NAU counties use FIPS format "051 - Livingston" — extract just the county NAME (e.g. "Livingston", "Monroe").
     You MUST capture EVERY row in the COVERAGE table as a prfCoverageRow.
     The TOTALS row at the bottom (436.74 / $168,166 / etc.) provides the policy-level totals.
   - Top COVERAGE summary table: Extract EACH row as a prfCoverageRow with:
     county, type (HAYING/GRAZING/etc.), coverage level (e.g. 85 = 0.85 or 85% = 0.85), productivity factor (e.g. 150% = 1.50),
     acres, liability, premium (gross), subsidy, netPremium, billingDate
   - The TOTALS row at the bottom of the COVERAGE table gives the policy-wide totals:
     prfTotalAcres, prfGrossPremium (PREMIUM total), prfSubsidy, prfNetPremium

   === R&H FORMAT (detail rows) ===
   - Detail rows (one per interval line in the "RAINFALL INDEX CROP INSURANCE SUMMARY OF COVERAGE" section):
     Each interval line has: Line#, interval code embedded in YLD# (e.g. "YLD# MAYJUNNIRR BU" → intervalCode="MAYJUN"),
     farm unit number, farm number, county, grid ID (e.g. 27507), county base value (NOT acres — ignore this for acre calculations), acre protection value (NOT acres — ignore this too),
     total acres for the grid (the FIRST/TOP number in the acres column — this is totalGridAcres), insured acres (the SECOND/BOTTOM number = totalGridAcres × percentValue), percent of value (%), total protection ($liability),
     productivity factor, premium
   - CRITICAL: Each detail row specifies its COUNTY (e.g. "1.000 GENESEE GRID ID: 27509"). You MUST set the county field
     on each prfIntervals entry to match the county shown on that detail row. Different intervals may be in different counties.
   - CRITICAL: Each detail row also specifies its TYPE (HAYING or GRAZNG/GRAZING). This appears on the sub-line
     (e.g. "1.0 HAYING" or "6.0 GRAZNG"). You MUST set the type field on each prfIntervals entry.
   - Interval code extraction: YLD# line format is "YLD# [CODE]NIRR BU" or "YLD# [CODE]IRR BU" or "YLD# [CODE]INT BU" → strip NIRR/IRR/INT/BU

   === NAU COUNTRY FORMAT (detail blocks per grid) ===
   - NAU organizes detail data by Grid ID blocks. Each block starts with "Grid ID: NNNNN" and contains a sub-table.
   - Sub-table columns: Index Interval # | Unit | Index Interval | % of value | Insured Acres by Grid ID/Interval | Policy Prot/acre | Liability | Premium | Subsidy | Insured Premium
   - The Grid ID block header area shows: Crop (PRF), Insurable Acres, Trigger Grid Index (85%), Plan (RI), Insured Acres, Productivity Factor (150%), Type/Intended Use (Haying or Grazing)
   - Each COLUMN in the sub-table is a separate interval. Extract each column as a separate prfIntervals entry:
     * intervalCode from "Index Interval" row (e.g. "MARAPRNIRR" → "MARAPR", "JUNJULNIRR" → "JUNJUL", "MAYJUNNIRR" → "MAYJUN", "AUGSEPNIRR" → "AUGSEP")
     * percentValue from "% of value" row (e.g. 20 → 0.20, 40 → 0.40, 50 → 0.50)
     * insuredAcres from "Insured Acres by Grid ID/Interval" row (this is PER-INTERVAL acres, NOT the grid total)
     * liability from "Liability" row
     * premium from "Insured Premium" row (this is the NET premium, after subsidy) — use this as the premium value
     * gridId = the Grid ID number (e.g. 27510, 27810)
     * county: CRITICAL — each grid detail page shows "County: NNN - CountyName" or "State: NN-NY County: NNN - CountyName" in its header area.
       Extract the county NAME from that page header (e.g. "County: 055 - Monroe" → "MONROE").
       If the document header lists multiple counties (e.g. "County: 055-MONROE, 051-LIVINGSTON"), look at each grid's
       INDIVIDUAL page to determine which county that specific grid belongs to.
       Different grids may be in different counties even though they share the same policy.
     * type = from "Type/Intended Use:" (e.g. "Haying" → "HAYING", "Grazing" → "GRAZING"). Normalize to uppercase.
   - ALSO extract the MPCI Premium Summary table (usually last data page) for accurate policy-level premium totals:
     This table has per-county rows with: County (FIPS code like "051" or "055"), Acres, Protection, Gross Premium, Amount of Subsidy, Insured Premium, Billing Date.
     Map FIPS codes to county names using the Summary of Coverage table (e.g. "051" = Livingston, "055" = Monroe).
     Use this to populate prfCoverageRows (one per county row) and the policy totals (prfTotalAcres, prfGrossPremium, prfSubsidy, prfNetPremium, billingDate).
     Include ALL county rows from the Premium Summary, even those with 0 acres — set acres=0 for those rows.

   === COMMON RULES (both formats) ===
   - Interval code cleanup: strip NIRR/IRR/INT/BU suffixes, remove hyphens
     e.g. "MAYJUNNIRR" → "MAYJUN", "JULAUGNIRR" → "JULAUG", "APR-MAYINT" → "APRMAY", "JUN-JULINT" → "JUNJUL", "AUG-SEPINT" → "AUGSEP"
   - Normalize type: "Haying" → "HAYING", "GRAZNG" → "GRAZING", "Grazing" → "GRAZING"
   - documentType: "PRF"

Return this exact JSON structure (use null for missing fields, empty arrays [] if section not found):
{
  "documentType": "PP_REPORT" | "APH_DATABASE" | "SOI_CROP" | "SOI_DRP" | "LRP" | "PRF",
  "aip": "NAU" | "RAIN_AND_HAIL" | "OTHER",
  "name": "insured entity name exactly as printed",
  "dba": "DBA name if present, else null",
  "policyNumber": "e.g. NY-944-6076784-26 or MP-0843027",
  "cropYear": 2026,
  "address": "full street address",
  "phone": "phone number",
  "agent": "agent full name",
  "agency": "agency name",
  "insurer": "insurance company name",
  "county": "primary county name",
  "state": "2-letter state code e.g. NY",
  "approvedYields": [
    {
      "county": "Orleans",
      "crop": "CORN",
      "practice": "Non-Irrigated",
      "type": "GSG",
      "rawType": "GSG",
      "plan": "RP",
      "coverageLevel": 0.75,
      "approvedYield": 170,
      "unitCount": 1,
      "acres": 245.6
    }
  ],
  "premiumTotals": {
    "_comment": "The statement's OWN policy Totals row, copied verbatim — never summed by you. It is the only thing that can reveal a row you missed, so extract it even when it seems redundant. Null any figure the document does not state.",
    "grossPremium": 66997,
    "subsidy": 40197,
    "netPremium": 26800,
    "liability": 448101,
    "insuredAcres": 172.60
  },
  "ppEligibility": [
    {
      "county": "Orleans",
      "crop": "CORN",
      "practice": "Non-Irrigated",
      "type": "GSG",
      "rawType": "GSG",
      "yr1": 728.92,
      "yr1Year": 2022,
      "yr2": 870.41,
      "yr2Year": 2023,
      "yr3": 661.74,
      "yr3Year": 2024,
      "yr4": 918.05,
      "yr4Year": 2025,
      "maxEligAcres": 918.05,
      "maxEligYear": 2025,
      "aphYield": 170,
      "coverageLevel": 0.75,
      "ppLiabilityPerAcre": 297.41
    }
  ],
  "drpEndorsements": [
    {
      "endNum": 40,
      "quarter": "Q1 2025",
      "practiceType": "JANMARY3Q1",
      "effectiveDate": "10/14/2024",
      "endDate": "03/31/2025",
      "insShr": 1.0,
      "declaredMilkProd": 1000000,
      "expMilkPerCow": 25000,
      "classIII": 1,
      "coverageLevel": 0.95,
      "projectedPrice": 20.15,
      "triggerPrice": 19.14,
      "expRevGuar": 181928,
      "liability": 181928,
      "totalPremium": 5200,
      "subsidy": 2548,
      "producerPremium": 2652
    }
  ],
  "lrpEndorsements": [
    {
      "commodity": "Feeder Cattle",
      "type": "SW2",
      "county": "Yates",
      "salesDate": "2025-08-26",
      "endDate": "2026-03-24",
      "endorsementLength": 30,
      "numberOfHead": 88,
      "targetWeight": 9.0,
      "coveragePrice": 350.68,
      "insuredValue": 277739,
      "totalPremium": 21745,
      "subsidy": 10178,
      "netPremium": 11567,
      "billingDate": "2026-05-01"
    }
  ],
  "scoEcoEndorsements": [
    {
      "county": "Monroe",
      "crop": "SOYBEANS",
      "type": "NTS",
      "practice": "Non-Irrigated",
      "plan": "SCO-RP",
      "coverageLevel": 0.86,
      "acres": 185.5
    }
  ],
  "prfCoverageRows": [
    {
      "county": "Genesee",
      "type": "HAYING",
      "acres": 210.86,
      "liability": 120176,
      "premium": 12410,
      "subsidy": 7759,
      "netPremium": 4651,
      "coverageLevel": 0.85,
      "productivityFactor": 1.50,
      "billingDate": "2026-09-01"
    }
  ],
  "prfGrossPremium": null,
  "prfNetPremium": null,
  "prfSubsidy": null,
  "prfCoverageLevel": null,
  "prfTotalAcres": null,
  "prfBillingDate": null,
  "prfIntervals": [
    {
      "county": "Genesee",
      "type": "HAYING",
      "gridId": "27507",
      "intervalCode": "MAYJUN",
      "farmUnit": 1,
      "totalGridAcres": 139.68,
      "insuredAcres": 69.84,
      "percentValue": 0.50,
      "liability": 39804,
      "productivityFactor": 1.50,
      "premium": 1612
    }
  ]
}

IMPORTANT RULES:
- rawType field: for every approvedYields and ppEligibility row, set rawType to the EXACT characters you read from the Type field in that block header or table cell (e.g. "GSG", "GSS", "NTS", "W", "gsg", "G5G"). Do NOT normalize it — copy it verbatim. If the Type field was blank, set rawType to null. This field is used to detect misreads.
- Crop names must be uppercase: CORN, SOYBEANS, DRY BEANS, WHEAT, PROCESSING BEANS, GRAIN SORGHUM, POTATOES
- Strip -RP, -YP suffixes from crop names in Rain & Hail format (CORN-RP → CORN)
- NAU PP crop names: strip "Organic(Certified)" and "Non-Irr." suffixes — "Corn Organic(Certified) Non-Irr." → "CORN"
- NAU PP practice normalization: "Non-Irrigated" → practice="Non-Irrigated". "Organic(Certified) Non-Irr." → practice="Organic". These must match the APH practice values exactly.
- NAU PP acre values: strip asterisks (728.92* → 728.92) and commas (1,643.91 → 1643.91) before returning as numbers
- Coverage levels are decimals: 75% = 0.75
- For NAU PP reports, approvedYields = [] (PP reports don't have APH yields)
- For SOI_CROP documents, ALWAYS populate approvedYields — never return [] for SOI_CROP, even for WHEAT
- For Rain & Hail PP worksheets: approvedYields = [] always. Do NOT set aphYield from the YIELD# column — that column is a sequential unit index number (1.0, 2.0, etc.), NOT a bu/acre yield. Only set ppLiabilityPerAcre if PER ACRE > 0, else set ppLiabilityPerAcre=null. Never put PP LIABILITY PER ACRE into approvedYield — that is a dollar value not a yield.
- R&H PP county names: extract exactly as shown in the document (e.g. "ORLEANS", "GENESEE"). Do NOT lowercase.
- R&H PP multiple types: CORN/GSG and CORN/GSS in the same county are TWO SEPARATE ppEligibility rows with different ppLiabilityPerAcre values. Never merge them.
- R&H PP optional units (OU): collapse multiple rows with same county+crop+type to ONE row using MAX per_acre value.
- R&H PP zero per_acre: include the row but set ppLiabilityPerAcre=null (not 0).
- R&H PP maxEligAcres: use the TYPE sub-row value from Section A when present; fall back to CROP-level row when no TYPE sub-row.
- For APH Database docs, extract approvedYields from "Apprv Yld" field on each crop page
- County names: extract just the name, not the code (073 - Orleans → "Orleans")
- For NAU PP reports, maxEligAcres = max of yr1/yr2/yr3/yr4 values
- DRY BEANS approved yield is in lbs/acre (typically 1000-2500). CORN in bu (100-250). SOYBEANS in bu (30-80). WHEAT in bu (40-100). POTATOES in cwt/acre (hundredweight, typically 150-500 cwt/ac).
- POTATOES price: potato crop insurance uses a price in $/cwt (dollars per hundredweight) set in the Special Provisions per county. Extract the price election exactly as shown on the SOI or APH document (e.g. "$10.00 per cwt" → price: 10.00). Store in the price field of the approvedYields row. Unit is always "cwt" for potatoes.
- POTATOES crop code: NAU uses "0084 - Potatoes", some documents may use "0093 - Potatoes" — both normalize to "POTATOES"
- POTATOES type codes: PTO = fresh/tablestock, CHIP = chipping/processing, SEED = certified seed, ALL = no sub-type specified (NAU PP reports often show ALL when no breakdown). Extract the type code exactly as shown. Use rawType to record the exact characters read.
- POTATOES practice: "No Practice Specified" → normalize to "" (empty string, not null). Do not error on this — potatoes often have no irrigated/non-irrigated distinction.
- For SOI_DRP documents: ALWAYS populate drpEndorsements — every single endorsement row must be extracted
- Quarter MUST be derived for every endorsement — never leave quarter as null. Use the end date year + calendar quarter if practice type code is unclear
- declaredMilkProd is ALWAYS in pounds (never hundredweight). Typical range 500,000 to 5,000,000 lbs per endorsement
- classIII: READ THE "Class III%" COLUMN ON THE ENDORSEMENT ROW (the column may also be labeled "Decl Comp III%" or "Class III%"):
  * If Class III% = 100 (or "100 %") → classIII = 1 (pure Class III pricing)
  * If Class III% = 0 (or "0") → classIII = 0 (pure Class IV pricing). THE COLUMN VALUE IS FINAL — do not override it.
  * If Class III% is blank/absent (the column is completely missing or empty) → look at the practice type code structure: in NAU codes like "JULSEPY2Q3", the "Y2" means "Year 2" and "Q3" means "Quarter 3" — these are NOT class indicators. Instead look for a standalone suffix: codes ending in "C3" or containing "-III" are Class III; codes ending in "C4" or containing "-IV" are Class IV; if still unclear default to classIII = 0
  * NEVER default to classIII = 1 without evidence — always read the column value first
  * IMPORTANT: A "0" in the Class III% column means Class IV, NOT Class III. Do not let the practice type code override an explicit column value.
  * EXAMPLE: End# 1, practiceType "JULSEPY2Q3", Class III% = 0 → classIII = 0 (Class IV). The "Y2" in the code means Year 2, NOT Class II or Class III.
  * EXAMPLE: End# 2, practiceType "OCTDECY2Q4", Class III% = 0 → classIII = 0 (Class IV).
- For PRF documents, prfIntervals must have one entry per detail row (interval line). percentValue is decimal: 50% = 0.50
- CRITICAL: Each prfIntervals entry MUST include county (from the detail row's county field) and type ("HAYING" or "GRAZING").
- CRITICAL: prfCoverageRows MUST have one entry per row in the COVERAGE summary table (top of page 1).
  Multi-county PRF SOIs will have multiple rows — one per county/type combination. Extract ALL of them.
  The TOTALS row at the bottom goes into prfTotalAcres, prfGrossPremium, prfSubsidy, prfNetPremium.
- PRF interval type normalization: "GRAZNG" → "GRAZING", "Grazing" → "GRAZING", "HAYING" → "HAYING", "Haying" → "HAYING"
- productivityFactor is decimal: 150% = 1.50
- PRF COVERAGE table header extraction (CRITICAL — these come from the COVERAGE summary table at the top, NOT the detail rows):
  * prfGrossPremium = the PREMIUM column value from the COVERAGE table (e.g. 109570 for Oakfield Alabama). This is the GROSS premium BEFORE subsidy.
  * prfSubsidy = the SUBSIDY column value from the COVERAGE table (e.g. 68516)
  * prfNetPremium = the NET PREMIUM column value from the COVERAGE table (e.g. 41054). This equals PREMIUM minus SUBSIDY.
  * prfCoverageLevel = the LEVEL column as a decimal (e.g. 85 → 0.85)
  * prfTotalAcres = the FINAL ACRES (or ACRES) column from the COVERAGE table (e.g. 1878.55). This is the TOTAL insured acres for the entire policy.
  * prfBillingDate = the BILLING DATE column (e.g. "2026-09-01")
- PRF interval "Premium" column: the Premium shown on each detail row is the NET premium (after subsidy) for that interval, NOT the gross. The sum of all interval premiums = prfNetPremium (NOT prfGrossPremium).
- PRF interval acres: Each detail row has a complex layout. The columns from left to right are:
  County Base Value (e.g. 447.00) | Acre Protection (e.g. 379.95 or 569.93) | Total Acres (top number) / Insured Acres (bottom number) | Percent of Value
  CRITICAL: "County Base Value" and "Acre Protection" are NOT acre fields — do NOT use them for totalGridAcres or insuredAcres.
  * totalGridAcres = the TOP number in the "Total Acres / Insured Acres" column. This is the grid's total insured acres (e.g. 249.91 for Ranney Farms, 1476.14 for Oakfield Alabama grid 27807). It appears ABOVE the insured acres number.
  * insuredAcres = the BOTTOM number in the "Total Acres / Insured Acres" column. This equals totalGridAcres × percentValue (e.g. 249.91 × 50% = 124.96). It appears BELOW the total acres number.
  * The sum of unique totalGridAcres across all grids should equal prfTotalAcres from the COVERAGE table.
  * If the grid has a productivity factor of 100% (not 150%), the county base value (447.00) may look like a plausible acre number — it is NOT. Always use the "Total Acres" column, not "County Base Value".
- coverageLevel for PRF is decimal: 85 on doc = 0.85
- For SOI_DRP documents with many endorsements: you MUST output ALL endorsements in the drpEndorsements array. Do not stop early. The JSON must be complete and valid — every opened [ or { must be closed. If you cannot fit all endorsements, output fewer fields per endorsement rather than truncating the array.
- Return ONLY the JSON object. No other text. Ensure the JSON is complete and parseable — the last character must be }.`;

const SIZE_LIMIT_BYTES = 2.5 * 1024 * 1024;

let pdfjsLib = null;
async function getPDFJS() {
  if (pdfjsLib) return pdfjsLib;
  await new Promise((resolve, reject) => {
    if (window.pdfjsLib) { pdfjsLib = window.pdfjsLib; resolve(); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      pdfjsLib = window.pdfjsLib;
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return pdfjsLib;
}

// ── isEvalSupported: false — CVE-2024-4367, and it is NOT optional ──────────
// pdf.js <= 4.1.392 compiles glyph outlines with `new Function()` inside
// FontFaceObject.getPathGenerator, so a crafted font in a PDF executes attacker
// JavaScript in THIS origin — the one holding cloverag_access_token and
// cloverag_refresh_token in localStorage. Verified in the shipped 3.11.174
// bundle: the sink is guarded by `isEvalSupported && FeatureTest.isEvalSupported`,
// and the else branch right beside it interprets the same glyph commands. So
// false = identical rendering, no eval. This is Mozilla's documented workaround
// for versions predating the fix, and we are pinned to 3.11.174 because every
// 4.x+ CDN build is ESM-only while this loader is a <script> tag waiting on the
// window.pdfjsLib global (measured: pdf.min.js is 404 on 4.10.38 / 5.7.284 / 6.2.108).
//
// EVERY getDocument() that renders a customer-supplied PDF must pass this.
// Enforced both ways by tools/check-pdf-eval.js.
// ── THE DOCUMENT'S OWN TEXT ──────────────────────────────────────────────────
// A worksheet that carries a text layer can be READ rather than described. That matters
// for an appraisal: the bushels on it are production-to-count on a federal claim, and a
// number lifted out of the file is checkable against the file while a number a model
// reported is not.
//
// Returns null — never a partial string — when the file has no text layer to read, so a
// caller cannot mistake a scan for an empty document. `isEvalSupported: false` for the
// same reason every other getDocument here passes it; check-pdf-eval enforces it.
async function pdfText(file) {
  try {
    const pdfjs = await getPDFJS();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer, isEvalSupported: false }).promise;
    const pages = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const tc = await page.getTextContent();
      // Group items onto lines by their y position: the parser reads line-oriented
      // records ("7.3 Doughy …"), and a flat join of every item would run the whole page
      // into one line and match nothing.
      const rows = new Map();
      for (const it of tc.items) {
        if (!it || typeof it.str !== "string") continue;
        const y = Math.round((it.transform && it.transform[5]) || 0);
        const row = rows.get(y) || [];
        row.push({ x: (it.transform && it.transform[4]) || 0, w: it.width || 0, s: it.str });
        rows.set(y, row);
      }
      // ⚠ THE COLUMN GAPS ARE LOAD-BEARING — DO NOT COLLAPSE THEM. This first joined the
      // items with a single space and squashed every run of whitespace, which reads fine
      // and destroys the one thing a form's layout carries: where one field ends and the
      // next begins. `County: 069-Ontario` then ran straight into `Unit No.:` on the same
      // line, and the county came out NULL because the pattern that reads it stops at a
      // column gap. A wide gap is rendered as several spaces, the way a column-preserving
      // text dump does, so the same patterns read both.
      const lines = [...rows.entries()].sort((a, b) => b[0] - a[0])
        .map(([, row]) => {
          const sorted = row.sort((a, b) => a.x - b.x);
          let line = "", cursor = null;
          for (const r of sorted) {
            if (cursor != null) {
              const gap = r.x - cursor;
              line += gap > 6 ? "   " : (gap > 0.5 || /\S$/.test(line) ? " " : "");
            }
            line += r.s;
            cursor = r.x + r.w;
          }
          return line.replace(/[ \t]+$/, "");
        })
        .filter((l) => l.trim());
      pages.push(lines.join("\n"));
    }
    const text = pages.join("\n");
    return text.trim().length >= 40 ? text : null;
  } catch { return null; }
}

// The positioned text items of every page, for a parser that groups lines itself
// (src/fsa156ez.js linesFromTextItems — FSA's form prints a label and its value a point
// apart, which the exact-y grouping above splits). Null when there is no text layer.
async function pdfTextItems(file) {
  const pdfjs = await getPDFJS();
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer, isEvalSupported: false }).promise;
  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) pages.push((await (await pdf.getPage(i)).getTextContent()).items || []);
  return pages.some((p) => p.some((it) => it && typeof it.str === "string" && it.str.trim())) ? pages : null;
}

async function pdfToImages(file, scale = 1.5, quality = 0.82) {
  const pdfjs = await getPDFJS();
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer, isEvalSupported: false }).promise;
  const images = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width; canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    images.push(canvas.toDataURL('image/jpeg', quality).split(',')[1]);
  }
  return images;
}

async function buildContentBlocks(file) {
  if (file.size <= SIZE_LIMIT_BYTES) {
    const raw = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return [{ type: "document", source: { type: "base64", media_type: "application/pdf", data: raw } }];
  }
  console.log(`PDF too large (${(file.size/1024/1024).toFixed(1)}MB), compressing to images...`);
  for (const [scale, quality] of [[1.5, 0.80], [1.2, 0.72], [1.0, 0.65], [0.8, 0.55]]) {
    const images = await pdfToImages(file, scale, quality);
    const totalBytes = images.reduce((s, img) => s + (img.length * 3) / 4, 0);
    if (totalBytes <= SIZE_LIMIT_BYTES) {
      return images.map(img => ({ type: "image", source: { type: "base64", media_type: "image/jpeg", data: img } }));
    }
  }
  const images = await pdfToImages(file, 0.8, 0.55);
  return images.map(img => ({ type: "image", source: { type: "base64", media_type: "image/jpeg", data: img } }));
}

async function extractPDFData(file) {
  // Helper to attempt extraction with given content blocks
  async function attemptExtraction(contentBlocks) {
    // adminApiFetch refreshes the session + retries once on a 401, so a stale
    // access token self-heals instead of surfacing as an instant "HTTP 401" fail.
    const response = await adminApiFetch("/api/extract", {
      method: "POST",
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 16000,
        messages: [{ role: "user", content: [...contentBlocks, { type: "text", text: EXTRACTION_PROMPT }] }]
      })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error?.message || "HTTP " + response.status);
    }
    return response;
  }

  // First attempt: send as PDF (fastest, preserves text layer)
  let contentBlocks = await buildContentBlocks(file);
  let response;
  try {
    response = await attemptExtraction(contentBlocks);
  } catch(err) {
    // If token limit exceeded, retry with aggressive image compression
    const isTokenError = err.message && (
      err.message.includes("prompt is too long") ||
      err.message.includes("too many tokens") ||
      err.message.includes("maximum") ||
      err.message.includes("context")
    );
    if (isTokenError && contentBlocks[0]?.type === "document") {
      console.warn(`Token limit hit for ${file.name} — retrying with image compression...`);
      // Force image compression at progressively lower quality
      for (const [scale, quality] of [[1.0, 0.60], [0.8, 0.50], [0.7, 0.42]]) {
        try {
          const images = await pdfToImages(file, scale, quality);
          contentBlocks = images.map(img => ({ type: "image", source: { type: "base64", media_type: "image/jpeg", data: img } }));
          response = await attemptExtraction(contentBlocks);
          console.log(`✅ Compressed extraction succeeded at scale=${scale} quality=${quality}`);
          break;
        } catch(retryErr) {
          if (!retryErr.message?.includes("prompt is too long") && !retryErr.message?.includes("too many tokens")) {
            throw retryErr; // non-token error, bail out
          }
          console.warn(`Still too large at scale=${scale}, trying lower quality...`);
        }
      }
      if (!response) throw new Error("PDF too large even after maximum compression. Try splitting the document.");
    } else {
      throw err;
    }
  }
  const data = await response.json();
  const raw = data.content.map(b => b.text || '').join('');
  const s = raw.indexOf('{');
  if (s === -1) throw new Error("Could not find JSON in response");

  // Try clean parse first
  const e = raw.lastIndexOf('}');
  let parsed = null;
  if (e !== -1) {
    try { parsed = JSON.parse(raw.slice(s, e + 1)); } catch(_) {}
  }

  // If clean parse failed, JSON was truncated — repair and flag it
  if (!parsed) {
    function repairJSON(str) {
      let t = str.replace(/,\s*$/, '');
      const stack = [];
      let inStr = false, esc = false;
      for (const ch of t) {
        if (esc) { esc = false; continue; }
        if (ch === '\\' && inStr) { esc = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '{' || ch === '[') stack.push(ch);
        else if (ch === '}' || ch === ']') stack.pop();
      }
      for (let i = stack.length - 1; i >= 0; i--) {
        t += stack[i] === '{' ? '}' : ']';
      }
      return t;
    }
    const repaired = repairJSON(raw.slice(s));
    try {
      parsed = JSON.parse(repaired);
      parsed._repaired = true;
      console.warn("⚠️ JSON truncated and repaired — will run continuation passes");
    } catch(err2) {
      throw new Error("JSON parse failed even after repair: " + err2.message);
    }
  }

  // ── CONTINUATION LOOP for DRP documents ─────────────────────────────────
  // DRP SOIs can have 74+ endorsements across 3+ pages. The AI can produce
  // valid but INCOMPLETE JSON — stopping cleanly without extracting all rows.
  // We ALWAYS run at least one continuation pass after any DRP extraction.
  // Loop until AI returns [], no new endNums, or 10-pass safety cap.
  if (parsed.documentType === "SOI_DRP" && parsed.drpEndorsements?.length) {
    const seenEndNums = new Set(parsed.drpEndorsements.map(e => e.endNum));
    let passNum = 0;
    const MAX_PASSES = 10;

    while (passNum < MAX_PASSES) {
      passNum++;
      const lastEndNum = parsed.drpEndorsements[parsed.drpEndorsements.length - 1]?.endNum;
      console.log(`DRP continuation pass ${passNum}: last end# = ${lastEndNum}, total so far = ${parsed.drpEndorsements.length}`);

      const continuePrompt = `This is a DRP Schedule of Insurance for ${parsed.name || 'the insured'} (policy ${parsed.policyNumber || ''}).
I have extracted endorsements and the lowest end# I have so far is ${lastEndNum}.
Endorsements are listed in DESCENDING order (highest end# first on page 1, lowest end# last).
Extract ONLY the endorsement rows with end# LESS THAN ${lastEndNum} that I have not yet captured.
Return ONLY a JSON array [ ] of those endorsements. Each object:
{ "endNum": number, "quarter": "e.g. OCTDECY2Q4", "type": "Class", "effectiveDate": "MM/DD/YYYY",
  "endDate": "MM/DD/YYYY", "insShr": 1.0, "declaredMilkProd": number, "expMilkPerCow": number,
  "classIII": 0 or 1, "coverageLevel": 0.95, "triggerPrice": number, "expRevGuar": number,
  "liability": number, "totalPremium": number, "subsidy": number, "producerPremium": number }
If there are NO endorsements with end# below ${lastEndNum}, return exactly: []
Return ONLY the JSON array, nothing else, no explanation.`;

      try {
        const r2 = await adminApiFetch("/api/extract", {
          method: "POST",
          body: JSON.stringify({
            model: "claude-sonnet-4-6",
            max_tokens: 16000,
            messages: [{ role: "user", content: [...contentBlocks, { type: "text", text: continuePrompt }] }]
          })
        });
        if (!r2.ok) { console.warn(`DRP pass ${passNum} HTTP error`); break; }
        const d2 = await r2.json();
        const raw2 = d2.content.map(b => b.text || '').join('');
        const a1 = raw2.indexOf('['), a2 = raw2.lastIndexOf(']');
        if (a1 === -1 || a2 === -1) { console.warn(`DRP pass ${passNum}: no array in response`); break; }

        let more;
        try {
          more = JSON.parse(raw2.slice(a1, a2 + 1));
        } catch(_) {
          // Array itself may be truncated — try repairing it
          try {
            const fix = raw2.slice(a1).replace(/,\s*$/, '') + ']';
            more = JSON.parse(fix);
          } catch(_2) { console.warn(`DRP pass ${passNum}: array parse failed`); break; }
        }

        if (!Array.isArray(more) || more.length === 0) {
          console.log(`✅ DRP pass ${passNum}: complete — AI returned []`);
          break;
        }

        const newEnds = more.filter(e => e.endNum != null && !seenEndNums.has(e.endNum));
        if (newEnds.length === 0) {
          console.log(`✅ DRP pass ${passNum}: complete — no new end#s`);
          break;
        }

        newEnds.forEach(e => seenEndNums.add(e.endNum));
        parsed.drpEndorsements = [...parsed.drpEndorsements, ...newEnds];
        console.log(`✅ DRP pass ${passNum}: +${newEnds.length} endorsements → total ${parsed.drpEndorsements.length}`);

      } catch(e2) {
        console.warn(`DRP pass ${passNum} exception:`, e2.message);
        break;
      }
    }
    console.log(`DRP extraction done: ${parsed.drpEndorsements.length} endorsements total (${passNum} continuation passes)`);
  }

  // ── NAU APH BLOCK VERIFICATION PASS ─────────────────────────────────────
  // NAU APH documents use a two-column layout. The main extraction pass
  // frequently misses the right column. This second pass independently
  // inventories every block header and reconciles any missing rows.
  if (parsed.documentType === "APH_DATABASE" && parsed.aip !== "RAIN_AND_HAIL") {
    console.log("NAU APH: running block inventory verification pass...");
    const inventoryPrompt = `This is a NAU Country APH Database document.
Your ONLY job is to inventory every unit block on the detail pages (pages 3+).
Each page shows up to TWO blocks side by side — left column AND right column.
For EVERY block you see, read from its header: County, Type (GSG/GSS/NTS/etc), Unit#.
Then find the value labeled "Apprv Yld" anywhere in that block's summary rows.
Also find the most recent non-zero acres from the production history table in that block (the "Acres" column from the most recent year row that isn't "0Z").

Return ONLY a JSON array — one entry per block:
[
  { "county": "LIVINGSTON", "type": "GSG", "practice": "Non-Irrigated", "unit": "0001-0001-000", "approvedYield": 180, "acres": 774.50 },
  { "county": "MONROE", "type": "GSG", "practice": "Non-Irrigated", "unit": "0001-0001-000", "approvedYield": 200, "acres": 120.00 },
  { "county": "MONROE", "type": "GSG", "practice": "Organic", "unit": "0001-0002-000", "approvedYield": 160, "acres": 45.00 }
]
CRITICAL: "practice" comes from the Practice field in each block header. "NON IRR" → "Non-Irrigated", "NON-IRROC" → "Organic".
Two blocks with the same county+type but different practice (e.g. one NON IRR, one NON-IRROC) are TWO separate entries — never merge them.
Include ALL blocks — do not skip any column. Return ONLY the JSON array, nothing else.`;

    try {
      const invResp = await adminApiFetch("/api/extract", {
        method: "POST",
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 4000,
          messages: [{ role: "user", content: [...contentBlocks, { type: "text", text: inventoryPrompt }] }]
        })
      });
      if (invResp.ok) {
        const invData = await invResp.json();
        const invRaw = invData.content.map(b => b.text || "").join("");
        const a1 = invRaw.indexOf("["), a2 = invRaw.lastIndexOf("]");
        if (a1 !== -1 && a2 !== -1) {
          let inventory = null;
          try { inventory = JSON.parse(invRaw.slice(a1, a2 + 1)); } catch(_) {}
          if (Array.isArray(inventory)) {
            console.log(`NAU APH inventory: ${inventory.length} blocks:`, inventory.map(b => `${b.county}/${b.type}/${b.practice||"NO_PRACTICE"}=${b.approvedYield}`).join(", "));
            const existing = parsed.approvedYields || [];

            // Same approach as R&H: group by county+type, detect when main pass
            // merged different practices, and replace with inventory blocks.
            const invGroups = {};
            for (const block of inventory) {
              if (!block.county || !block.type || block.approvedYield == null) continue;
              const county = (block.county||"").toUpperCase().trim();
              const type = (block.type||"").toUpperCase().trim();
              const crop = (type === "NTS" || type === "W") ? (type === "W" ? "WHEAT" : "SOYBEANS") : "CORN";
              const practice = block.practice || "Non-Irrigated";
              const gkey = `${county}|${type}`;
              if (!invGroups[gkey]) invGroups[gkey] = [];
              invGroups[gkey].push({ county, crop, type, practice, approvedYield: block.approvedYield, acres: block.acres ?? null });
            }

            const exGroups = {};
            existing.forEach((ay, idx) => {
              const gkey = `${(ay.county||"").toUpperCase().trim()}|${(ay.type||"").toUpperCase().trim()}`;
              if (!exGroups[gkey]) exGroups[gkey] = [];
              exGroups[gkey].push({ ...ay, _idx: idx });
            });

            const reconciled = [];
            const handledExisting = new Set();

            for (const [gkey, invBlocks] of Object.entries(invGroups)) {
              const exBlocks = exGroups[gkey] || [];
              const invPractices = [...new Set(invBlocks.map(b => b.practice))];
              const exPractices = [...new Set(exBlocks.map(b => normalizePractice(b.practice)))];
              exBlocks.forEach(b => handledExisting.add(b._idx));

              if (invPractices.length > 1 && exPractices.length <= 1) {
                const template = exBlocks[0] || {};
                console.warn(`NAU APH: replacing merged ${gkey} with ${invBlocks.length} inventory blocks (practices: ${invPractices})`);
                for (const inv of invBlocks) {
                  reconciled.push({
                    county: inv.county, crop: inv.crop, type: inv.type,
                    practice: inv.practice, approvedYield: inv.approvedYield, unitCount: 1,
                    coverageLevel: template.coverageLevel || null, plan: template.plan || "RP",
                  });
                }
              } else {
                for (const inv of invBlocks) {
                  const exactMatch = exBlocks.find(b =>
                    !handledExisting.has(b._idx + 10000) &&
                    normalizePractice(b.practice).toUpperCase() === inv.practice.toUpperCase() &&
                    Math.abs((b.approvedYield||0) - inv.approvedYield) < 2
                  );
                  if (exactMatch) {
                    handledExisting.add(exactMatch._idx + 10000);
                    reconciled.push({ ...exactMatch, practice: inv.practice });
                  } else {
                    const yieldMatch = exBlocks.find(b =>
                      !handledExisting.has(b._idx + 10000) &&
                      Math.abs((b.approvedYield||0) - inv.approvedYield) < 2
                    );
                    if (yieldMatch) {
                      handledExisting.add(yieldMatch._idx + 10000);
                      reconciled.push({ ...yieldMatch, practice: inv.practice, crop: inv.crop });
                    } else {
                      reconciled.push({ ...inv, unitCount: 1, plan: (exBlocks[0]||{}).plan || "RP", coverageLevel: (exBlocks[0]||{}).coverageLevel ?? null });
                    }
                  }
                }
              }
            }

            existing.forEach((ay, idx) => { if (!handledExisting.has(idx)) reconciled.push(ay); });
            parsed.approvedYields = reconciled;
            console.log(`NAU APH reconciled: ${reconciled.length} rows`);
          }
        }
      }
    } catch(invErr) {
      console.warn("NAU APH inventory pass failed (non-fatal):", invErr.message);
    }
  }

  // ── R&H APH BLOCK VERIFICATION PASS ─────────────────────────────────────
  if (parsed.documentType === "APH_DATABASE" && parsed.aip === "RAIN_AND_HAIL") {
    console.log("R&H APH: running block inventory verification pass...");
    const rhInventoryPrompt = `This is a Rain & Hail / ACE Property and Casualty MPCI Acreage and Production Reporting Form (RH-5204).
Your ONLY job is to inventory every unit block on the production reporting pages (pages 3+).
Each page shows up to FOUR blocks arranged in columns — scan ALL columns on EVERY page.
For EVERY block you see, read from its header:
- County (e.g. "37 GENESEE" → "GENESEE", "63 NIAGARA" → "NIAGARA", "73 ORLEANS" → "ORLEANS")
- Crop (e.g. "RP-CORN" → "CORN", "RP-SOYBEANS" → "SOYBEANS", "RP-WHEAT" → "WHEAT")
- Practice: "NON IRR" → "Non-Irrigated", "NON-IRROC" → "Organic"
- Type: Read EXACTLY from the block header. Common types:
  * CORN blocks: GSG (grain) or GSS (silage) — never NTS
  * SOYBEANS blocks: NTS — never GSG or GSS
  * WHEAT blocks: W
  DO NOT confuse types between crops. SOYBEANS is ALWAYS type NTS. CORN is GSG or GSS.
Then find "Appr Yld" at the bottom of that block's summary section.

Return ONLY a JSON array — one entry per block:
[
  { "county": "GENESEE", "crop": "CORN", "type": "GSG", "practice": "Non-Irrigated", "approvedYield": 164 },
  { "county": "GENESEE", "crop": "CORN", "type": "GSG", "practice": "Organic", "approvedYield": 142 },
  { "county": "GENESEE", "crop": "SOYBEANS", "type": "NTS", "practice": "Non-Irrigated", "approvedYield": 45 },
  { "county": "GENESEE", "crop": "SOYBEANS", "type": "NTS", "practice": "Organic", "approvedYield": 26 }
]
CRITICAL RULES:
1. "NON IRR" → "Non-Irrigated", "NON-IRROC" → "Organic"
2. Two blocks with same county+crop+type but different practice = TWO SEPARATE entries
3. SOYBEANS type is ALWAYS "NTS", CORN type is "GSG" or "GSS", WHEAT type is "W"
4. Read "Appr Yld" (or "Apprv Yld") exactly — do not average or modify
Include ALL blocks from ALL pages. Return ONLY the JSON array.`;

    try {
      const rhInvResp = await adminApiFetch("/api/extract", {
        method: "POST",
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 4000,
          messages: [{ role: "user", content: [...contentBlocks, { type: "text", text: rhInventoryPrompt }] }]
        })
      });
      if (rhInvResp.ok) {
        const rhInvData = await rhInvResp.json();
        const rhInvRaw = rhInvData.content.map(b => b.text || "").join("");
        const ra1 = rhInvRaw.indexOf("["), ra2 = rhInvRaw.lastIndexOf("]");
        if (ra1 !== -1 && ra2 !== -1) {
          let rhInventory = null;
          try { rhInventory = JSON.parse(rhInvRaw.slice(ra1, ra2 + 1)); } catch(_) {}
          if (Array.isArray(rhInventory) && rhInventory.length > 0) {
            console.log(`R&H APH inventory: ${rhInventory.length} blocks:`, rhInventory.map(b => `${b.county}/${b.crop}/${b.type}/${b.practice||"?"}=${b.approvedYield}`).join(", "));
            
            const existing = parsed.approvedYields || [];
            
            // The inventory pass is AUTHORITATIVE for R&H APH.
            // It has correct practice, type, and yield for every block.
            // The main pass often merges organic+conventional or misreads types.
            // Strategy: REPLACE parsed.approvedYields entirely with inventory blocks,
            // inheriting coverageLevel from the main pass where we can match by county+crop.
            {
              // The inventory pass is AUTHORITATIVE for R&H APH.
              // It has correct practice, type, and yield for every block.
              // The main pass often merges organic+conventional or misreads types.
              // Strategy: REPLACE parsed.approvedYields entirely with inventory blocks,
              // inheriting coverageLevel from the main pass where we can match by county+crop.
            
            // Build a coverageLevel lookup from the main pass (keyed by county+crop)
            // Page 1 coverage table is usually extracted correctly even when unit blocks aren't
            const covLevelLookup = {};
            existing.forEach(ay => {
              const k = `${(ay.county||"").toUpperCase().trim()}|${(ay.crop||"").toUpperCase().trim()}`;
              if (ay.coverageLevel != null && ay.coverageLevel > 0 && !covLevelLookup[k]) {
                covLevelLookup[k] = { coverageLevel: ay.coverageLevel, plan: ay.plan || "RP" };
              }
            });
            
            // Build new approvedYields entirely from inventory
            const newYields = [];
            for (const block of rhInventory) {
              if (!block.county || !block.type || block.approvedYield == null) continue;
              const county = (block.county||"").toUpperCase().trim();
              const crop = block.crop ? (block.crop||"").toUpperCase().trim() : ((block.type||"").toUpperCase() === "NTS" ? "SOYBEANS" : ((block.type||"").toUpperCase() === "W" ? "WHEAT" : "CORN"));
              const type = (block.type||"").toUpperCase().trim();
              const practice = block.practice || "Non-Irrigated";
              
              // Enforce type rules: SOYBEANS=NTS, CORN=GSG/GSS, WHEAT=W
              let fixedType = type;
              if (crop === "SOYBEANS" && type !== "NTS") { console.warn(`R&H APH: fixing type ${type}→NTS for ${county}/${crop}`); fixedType = "NTS"; }
              if (crop === "WHEAT" && type !== "W") { console.warn(`R&H APH: fixing type ${type}→W for ${county}/${crop}`); fixedType = "W"; }
              
              const lookup = covLevelLookup[`${county}|${crop}`] || {};
              
              newYields.push({
                county,
                crop,
                type: fixedType,
                practice,
                approvedYield: block.approvedYield,
                unitCount: 1,
                coverageLevel: lookup.coverageLevel || null,
                plan: lookup.plan || "RP",
              });
            }
            
            console.log(`R&H APH: inventory authoritative — replacing ${existing.length} main-pass rows with ${newYields.length} inventory rows`);
            console.log(`R&H APH final:`, newYields.map(a => `${a.county}/${a.crop}/${a.type}/${a.practice}=${a.approvedYield} @${a.coverageLevel||"?"}`).join(", "));
            parsed.approvedYields = newYields;
            } // end inventory authoritative block
          }
        }
      }
    } catch(rhInvErr) {
      console.warn("R&H APH inventory pass failed (non-fatal):", rhInvErr.message);
    }
  }

  return parsed;
}

// ─────────────────────────────────────────────────────────────────────────────
// EXTRACTION VALIDATION
// Runs on every extracted document before it touches the DB.
// Returns { warnings: [], errors: [], cleaned: extracted }
// errors   → hard blocks (data is known wrong, nulled out)
// warnings → soft flags (surfaced in review UI, data preserved)
// ─────────────────────────────────────────────────────────────────────────────
function validateExtraction(extracted) {
  const warnings = [];
  const errors   = [];
  const e = JSON.parse(JSON.stringify(extracted)); // deep clone — don't mutate original

  const warn = (code, msg) => warnings.push({ code, msg });
  const err  = (code, msg) => errors.push({ code, msg });

  // ── IDENTITY CHECKS ───────────────────────────────────────────────────────
  if (!e.name || e.name === 'Unknown') warn('NO_NAME', 'Farmer name not found — verify before saving');
  if (!e.policyNumber)                 warn('NO_POLICY_NUM', 'Policy number not extracted');
  if (!e.cropYear)                     warn('NO_CROP_YEAR', 'Crop year not found — defaulting to current year');
  if (e.cropYear && (e.cropYear < 2020 || e.cropYear > 2030))
    err('BAD_CROP_YEAR', `Crop year ${e.cropYear} is outside expected range 2020–2030`);

  // ── SOI_CROP CHECKS ───────────────────────────────────────────────────────
  if (e.documentType === 'SOI_CROP' && e.approvedYields?.length) {
    const yields = e.approvedYields;

    // 1. No yields at all
    if (yields.length === 0)
      err('NO_YIELDS', 'SOI_CROP document has no approved yield rows — extraction likely failed');

    yields.forEach((y, i) => {
      const tag = `${y.county || '?'} ${y.crop || '?'}`;

      // 2. Approved yield range sanity (bu/ac)
      //    Corn: 50–300 | Soybeans: 20–100 | Wheat: 20–120 | Dry Beans: 10–40 | Potatoes: 100–600 cwt/ac | generic: 5–500
      const yieldRanges = {
        CORN: [50, 300], SOYBEANS: [20, 100], WHEAT: [20, 120],
        'DRY BEANS': [10, 40], BEANS: [10, 40], POTATOES: [100, 600],
      };
      const range = yieldRanges[(y.crop || '').toUpperCase()] || [5, 500];
      if (y.approvedYield != null) {
        if (y.approvedYield < range[0])
          warn('LOW_YIELD', `${tag}: approved yield ${y.approvedYield} bu/ac is very low (expected ≥${range[0]}) — verify APH`);
        if (y.approvedYield > range[1])
          warn('HIGH_YIELD', `${tag}: approved yield ${y.approvedYield} bu/ac seems high (expected ≤${range[1]}) — verify units`);
      } else {
        warn('NULL_YIELD', `${tag}: approved yield is missing`);
      }

      // 3. Coverage level sanity (must be 0.50–0.95, stored as decimal)
      if (y.coverageLevel != null) {
        // Catch common mistake: stored as percent (e.g. 80) instead of decimal (0.80)
        if (y.coverageLevel > 1 && y.coverageLevel <= 100) {
          warn('COVERAGE_AS_PCT', `${tag}: coverage level ${y.coverageLevel} looks like a percentage — auto-converting to ${(y.coverageLevel/100).toFixed(2)}`);
          e.approvedYields[i].coverageLevel = y.coverageLevel / 100;
        }
        const cl = e.approvedYields[i].coverageLevel;
        if (cl < 0.50 || cl > 0.95)
          err('BAD_COVERAGE', `${tag}: coverage level ${(cl*100).toFixed(0)}% is outside valid range 50–95%`);
      } else {
        warn('NULL_COVERAGE', `${tag}: coverage level is missing`);
      }

      // 4. Acres sanity
      if (y.acres != null) {
        if (y.acres <= 0)   err('ZERO_ACRES', `${tag}: insured acres is 0 or negative`);
        if (y.acres > 9999) warn('HIGH_ACRES', `${tag}: ${y.acres} acres seems very high — verify`);
      }

      // 5. Plan must be RP, ECO-RP, SCO, or known value
      const validPlans = ['RP', 'RP-HPE', 'ECO-RP', 'SCO', 'YP', 'APH'];
      if (y.plan && !validPlans.includes((y.plan||'').toUpperCase()))
        warn('UNKNOWN_PLAN', `${tag}: plan "${y.plan}" is not a recognised plan type`);
    });

    // 6. Duplicate acres across counties (existing check, now surfaced as warning too)
    const acreGroups = {};
    yields.forEach(y => {
      if (!y.acres || y.acres <= 0) return;
      const key = `${y.crop}|${y.acres}`;
      (acreGroups[key] = acreGroups[key] || []).push(y.county);
    });
    Object.entries(acreGroups).forEach(([key, counties]) => {
      if (counties.length > 1) {
        const [crop, acres] = key.split('|');
        err('DUPLICATE_ACRES', `${counties.join(' & ')} both show identical ${acres} acres for ${crop} — almost certainly a copy error. Acres nulled on duplicates.`);
        let first = true;
        e.approvedYields = e.approvedYields.map(y => {
          if (y.crop === crop && String(y.acres) === acres) {
            if (first) { first = false; return y; }
            return { ...y, acres: null };
          }
          return y;
        });
      }
    });

    // 7. Duplicate county+crop rows (should never happen — each county+crop is one row)
    const rowKeys = yields.map(y => `${y.county}|${y.crop}|${y.plan}`);
    const dupRows = rowKeys.filter((k, i) => rowKeys.indexOf(k) !== i);
    if (dupRows.length) err('DUPLICATE_ROWS', `Duplicate county+crop+plan rows: ${[...new Set(dupRows)].join(', ')}`);

    // 8. Price sanity (if present — some SOIs include it)
    yields.forEach(y => {
      if (y.price != null) {
        const priceRanges = { CORN: [3, 10], SOYBEANS: [8, 20], WHEAT: [4, 12], 'DRY BEANS': [20, 50], POTATOES: [10, 50], 'GRAIN SORGHUM': [3, 8] };
        const pr = priceRanges[(y.crop||'').toUpperCase()];
        if (pr && (y.price < pr[0] || y.price > pr[1]))
          warn('BAD_PRICE', `${y.county} ${y.crop}: price $${y.price}/bu is outside expected range $${pr[0]}–$${pr[1]}`);
      }
    });

    // ── THE THREE THINGS A HAND-LOAD KEPT GETTING WRONG ───────────────────
    // These are the import-time twins of the check-data assertions added
    // 2026-08-12. The checks catch it a DAY later, book-wide; these catch it
    // before it is ever written, on the one document in front of you.

    // 1. A billed row must state what the GROWER pays.
    //    Agri Sompo hand-loads landed gross with no split on 28 rows /
    //    $246,502, which contributed their full gross to the book and ZERO to
    //    every producer-premium total. It is not derivable — the subsidy rate
    //    depends on coverage level AND unit structure — so it has to come off
    //    the statement at load time or be keyed by hand later.
    //    CAT is the deliberate exception: subsidy == gross and the producer
    //    share really is nil.
    yields.forEach(y => {
      const gross = Number(y.totalPremium) || 0;
      if (!(gross > 0)) return;
      const sub = Number(y.subsidy) || 0;
      if (y.netPremium == null && Math.abs(gross - sub) > 1)
        err('PREMIUM_NO_SPLIT',
          `${y.county || '?'} ${y.crop || '?'} ${y.plan || ''}: gross $${gross.toLocaleString()} with no Insured/Producer Premium. ` +
          `Read it off the statement's premium table — it cannot be derived from the subsidy rate.`);
    });

    // 2. A crop whose TYPES are different products must say which type it is.
    //    CY Farms sat for months with one 1,903-acre Genesee corn line at
    //    $517.90/ac that was really grain at $532.49 and silage at $482.28,
    //    and an Orleans ONION line blending Reds at $25.80 with Yellows at
    //    $15.60. Onions are why this is not scoped to units of measure — both
    //    are CWT, so only the PRICE differs. A type is a different product
    //    whenever RMA prices it separately.
    const MULTI_TYPE_CROPS = new Set(['CORN', 'ONIONS', 'GRAPES', 'APPLES', 'CABBAGE', 'DRY BEANS', 'PROCESSING BEANS', 'BARLEY']);
    yields.forEach(y => {
      const crop = (y.crop || '').toUpperCase();
      if (!MULTI_TYPE_CROPS.has(crop)) return;
      if (String(y.type || '').trim() || String(y.rawType || '').trim()) return;
      if (!(Number(y.acres) > 0) && !(Number(y.liability) > 0)) return;   // nothing keyed yet
      err('UNTYPED_MULTI_TYPE_CROP',
        `${y.county || '?'} ${crop}: no type stated, and ${crop} types are different products RMA prices separately. ` +
        `One untyped row can be two crops blended into a per-acre figure that is neither.`);
    });

    // 3. The rows must reconcile to the document's OWN totals.
    //    This is the guard that catches a SILENTLY DROPPED row, which no
    //    per-row sanity check can see — on the 2026-08-06 ProAg load the
    //    parser quietly lost rows ($270,374 against a stated $305,771) and
    //    only the Totals row revealed it. Skipped when the totals were not
    //    extracted, and reported as VOID rather than passing silently.
    const T = e.premiumTotals || null;
    if (!T || (T.grossPremium == null && T.liability == null)) {
      warn('NO_PREMIUM_TOTALS',
        `The statement's policy Totals row was not extracted, so the rows could not be reconciled to it. ` +
        `A dropped row is invisible without it — verify the premium total by hand before saving.`);
    } else {
      const sum = (k) => yields.reduce((s, y) => s + (Number(y[k]) || 0), 0);
      [['grossPremium', 'totalPremium', 'gross premium'],
       ['netPremium', 'netPremium', 'producer premium'],
       ['liability', 'liability', 'liability']].forEach(([totKey, rowKey, label]) => {
        const stated = Number(T[totKey]);
        if (!(stated > 0)) return;
        const got = sum(rowKey);
        // 1% or $2, whichever is larger — absorbs the AIP's own per-row rounding
        // without absorbing a missing row.
        if (Math.abs(got - stated) > Math.max(2, stated * 0.01))
          err('PREMIUM_TOTALS_MISMATCH',
            `Extracted ${label} $${Math.round(got).toLocaleString()} does not match the statement's stated total ` +
            `$${Math.round(stated).toLocaleString()} (off $${Math.round(got - stated).toLocaleString()}). ` +
            `A row was probably missed — do not save until they tie.`);
      });
    }

  }

  // 9. APH_DATABASE-specific: null out acres (APH has historical acres, not 2026 planted acres),
  //    silage yield range and missing GSG partner check.
  //
  //    THIS WAS DEAD CODE until 2026-08-12. It sat nested inside the
  //    `documentType === 'SOI_CROP'` block above, and a document cannot be both
  //    — so none of it had ever run: not the acres nulling its own comment calls
  //    CRITICAL, not the silage bushels-vs-tons range, not the missing-GSG
  //    pairing warning. It is a sibling of the SOI_CROP block, not a child.
  //    Found by testing the guards added beside it and watching them stay silent
  //    on fixtures that should have tripped them.
  if (e.documentType === 'APH_DATABASE' && e.approvedYields?.length) {
    const yields = e.approvedYields;
    yields.forEach(y => { y.acres = null; }); // CRITICAL: APH never provides insured planted acres
      yields.forEach(y => {
        const type = (y.type||'').toUpperCase();
        const crop = (y.crop||'').toUpperCase();
        // GSS (silage) yields should be 5–40 ton/ac. If > 50 it was almost certainly extracted in bushels.
        if (type === 'GSS' && y.approvedYield != null) {
          if (y.approvedYield > 50)
            err('GSS_YIELD_TOO_HIGH', `${y.county} CORN/GSS: approved yield ${y.approvedYield} ton/ac is impossibly high — was this extracted as bushels instead of tons? Silage yields are typically 10–25 ton/ac.`);
          if (y.approvedYield < 3)
            warn('GSS_YIELD_LOW', `${y.county} CORN/GSS: approved yield ${y.approvedYield} ton/ac is very low — verify`);
        }
        // GSG (grain) yields should be 50–300 bu/ac
        if (type === 'GSG' && y.approvedYield != null) {
          if (y.approvedYield < 10)
            err('GSG_YIELD_TOO_LOW', `${y.county} CORN/GSG: approved yield ${y.approvedYield} bu/ac is impossibly low — was this extracted as tons instead of bushels?`);
        }
        // WHEAT yields should be 20–120 bu/ac. Values like 86 or 95 are almost certainly
        // ECO-RP coverage percentages ("86% - 95%") being misread as the APH yield.
        if ((y.crop||'').toUpperCase() === 'WHEAT' && y.approvedYield != null) {
          if (y.approvedYield > 120)
            err('WHEAT_YIELD_TOO_HIGH', `${y.county} WHEAT: approved yield ${y.approvedYield} bu/ac is impossibly high for wheat (max ~120 bu/ac) — check extraction.`);
          if ([86, 90, 95].includes(y.approvedYield))
            warn('WHEAT_YIELD_LOOKS_LIKE_COVERAGE_PCT', `${y.county}: approved yield ${y.approvedYield} bu/ac matches an ECO coverage % — may be a misread of ECO "86%-95%". Verify APH on SOI and re-import.`);


          if (y.approvedYield < 20)
            warn('WHEAT_YIELD_LOW', `${y.county} WHEAT: approved yield ${y.approvedYield} bu/ac is very low — verify.`);
        }
      });

      // For each county that has a CORN/GSS row, check if CORN/GSG is also present
      // (NAU two-column layout: both types appear on the same physical page — if one is missing, extraction likely failed)
      const gssCounties = yields.filter(y => (y.type||'').toUpperCase()==='GSS' && (y.crop||'').toUpperCase()==='CORN').map(y => y.county);
      const gsgCounties = yields.filter(y => (y.type||'').toUpperCase()==='GSG' && (y.crop||'').toUpperCase()==='CORN').map(y => y.county);
      gssCounties.forEach(county => {
        if (!gsgCounties.includes(county))
          warn('MISSING_GSG', `${county} has CORN/GSS but no CORN/GSG — NAU APH pages show both types side-by-side. The GSG column may have been missed. Re-upload if GSG data should exist for this county.`);
      });
      // Reverse check: GSG without GSS (less common but possible if farmer only has grain)
      // Don't warn on this — GSG-only is valid (not all corn farmers have silage)
  }

  // ── RAWTYPE AUDIT — approvedYields ────────────────────────────────────────
  // For every CORN row in approvedYields, check if rawType is ambiguous or contradicts type.
  // We flag these for manual confirmation on the import review screen — no silent auto-flip.
  const corrections = [];
  const cornYields = (e.approvedYields||[]).filter(y => (y.crop||'').toUpperCase() === 'CORN');
  cornYields.forEach((y, i) => {
    const resolvedType = (y.type||'').toUpperCase();
    const raw = (y.rawType||'').trim();
    const rawUp = raw.toUpperCase();
    const globalIdx = e.approvedYields.findIndex(ay => ay === y); // index in full array

    // Case 1: rawType doesn't match the resolved type (model normalized differently than it read)
    const rawIsGSG = rawUp === 'GSG';
    const rawIsGSS = rawUp === 'GSS';
    const rawIsClean = rawIsGSG || rawIsGSS;

    if (!rawIsClean) {
      // rawType is missing, blank, or garbled — flag for confirmation
      corrections.push({
        arrayName: 'approvedYields',
        idx: globalIdx,
        county: y.county,
        crop: y.crop,
        rawType: raw || null,
        resolvedType,
        approvedYield: y.approvedYield,
        reason: raw
          ? `Type field read as "${raw}" — unclear if GSG (grain) or GSS (silage)`
          : 'Type field was blank or unreadable',
      });
    } else if (rawUp !== resolvedType) {
      // rawType is clean (GSG/GSS) but contradicts what was stored
      corrections.push({
        arrayName: 'approvedYields',
        idx: globalIdx,
        county: y.county,
        crop: y.crop,
        rawType: raw,
        resolvedType,
        approvedYield: y.approvedYield,
        reason: `Type field reads "${raw}" but was stored as ${resolvedType} — please confirm`,
      });
    } else if (resolvedType === 'GSS' && y.approvedYield != null && y.approvedYield > 50) {
      // Yield clearly out of range for silage — flag even if rawType looks clean
      corrections.push({
        arrayName: 'approvedYields',
        idx: globalIdx,
        county: y.county,
        crop: y.crop,
        rawType: raw,
        resolvedType,
        approvedYield: y.approvedYield,
        reason: `Stored as GSS (silage) but yield ${y.approvedYield} is far above silage range (5–40 ton/ac) — likely GSG (grain)`,
        yieldContradictsType: true,
      });
    } else if (resolvedType === 'GSG' && y.approvedYield != null && y.approvedYield < 20) {
      corrections.push({
        arrayName: 'approvedYields',
        idx: globalIdx,
        county: y.county,
        crop: y.crop,
        rawType: raw,
        resolvedType,
        approvedYield: y.approvedYield,
        reason: `Stored as GSG (grain) but yield ${y.approvedYield} is far below grain range (50–300 bu/ac) — likely GSS (silage)`,
        yieldContradictsType: true,
      });
    }
  });

  // ── RAWTYPE AUDIT — ppEligibility ─────────────────────────────────────────
  // PP rows have no yield to cross-check — rawType is the only signal
  const cornPP = (e.ppEligibility||[]).filter(p => (p.crop||'').toUpperCase() === 'CORN');
  cornPP.forEach((p, i) => {
    const resolvedType = (p.type||'').toUpperCase();
    const raw = (p.rawType||'').trim();
    const rawUp = raw.toUpperCase();
    const globalIdx = e.ppEligibility.findIndex(pp => pp === p);

    const rawIsGSG = rawUp === 'GSG';
    const rawIsGSS = rawUp === 'GSS';
    const rawIsClean = rawIsGSG || rawIsGSS;

    if (!rawIsClean && (resolvedType === 'GSG' || resolvedType === 'GSS')) {
      corrections.push({
        arrayName: 'ppEligibility',
        idx: globalIdx,
        county: p.county,
        crop: p.crop,
        rawType: raw || null,
        resolvedType,
        approvedYield: null,
        reason: raw
          ? `PP type field read as "${raw}" — unclear if GSG or GSS`
          : 'PP type field was blank or unreadable',
      });
    } else if (rawIsClean && rawUp !== resolvedType) {
      corrections.push({
        arrayName: 'ppEligibility',
        idx: globalIdx,
        county: p.county,
        crop: p.crop,
        rawType: raw,
        resolvedType,
        approvedYield: null,
        reason: `PP type field reads "${raw}" but was stored as ${resolvedType} — please confirm`,
      });
    }
  });

  // ── DRP CHECKS ────────────────────────────────────────────────────────────
  if (e.documentType === 'SOI_DRP' && e.drpEndorsements?.length) {
    e.drpEndorsements.forEach((end, i) => {
      const tag = `End #${end.endNum || i+1}`;

      // Declared milk production: typical range 100,000–10,000,000 lbs per endorsement
      if (end.declaredMilkProd != null) {
        if (end.declaredMilkProd < 10000)
          warn('LOW_MILK', `${tag}: declared milk ${end.declaredMilkProd.toLocaleString()} lbs seems very low`);
        if (end.declaredMilkProd > 20000000)
          warn('HIGH_MILK', `${tag}: declared milk ${end.declaredMilkProd.toLocaleString()} lbs seems very high — verify units (should be lbs, not cwt)`);
      } else {
        warn('NULL_MILK', `${tag}: declared milk production is missing`);
      }

      // Insurance share: should be 0–1 (decimal) or flagged if >1 (stored as percent)
      if (end.insShr != null) {
        if (end.insShr > 1 && end.insShr <= 100) {
          warn('INS_SHR_AS_PCT', `${tag}: insurance share ${end.insShr} looks like a percentage — auto-converting to ${(end.insShr/100).toFixed(4)}`);
          e.drpEndorsements[i].insShr = end.insShr / 100;
        }
        if (e.drpEndorsements[i].insShr > 1)
          err('BAD_INS_SHR', `${tag}: insurance share ${end.insShr} is > 1 after conversion — invalid`);
      }

      // Coverage level: 0.70–0.95 for DRP
      if (end.coverageLevel != null) {
        if (end.coverageLevel > 1 && end.coverageLevel <= 100) {
          e.drpEndorsements[i].coverageLevel = end.coverageLevel / 100;
        }
        if (e.drpEndorsements[i].coverageLevel < 0.70 || e.drpEndorsements[i].coverageLevel > 0.95)
          warn('BAD_DRP_COVERAGE', `${tag}: DRP coverage level ${(e.drpEndorsements[i].coverageLevel*100).toFixed(0)}% outside typical range 70–95%`);
      }

      // Quarter must be 1–4
      if (end.quarter != null && (end.quarter < 1 || end.quarter > 4))
        err('BAD_QUARTER', `${tag}: quarter ${end.quarter} is not 1–4`);

      // Producer premium should not be negative
      if (end.producerPremium != null && end.producerPremium < 0)
        err('NEG_PREMIUM', `${tag}: producer premium is negative ($${end.producerPremium})`);
    });

    // Check classIII field is explicitly 0 or 1 — null would silently default to Class III
    e.drpEndorsements.forEach((end, i) => {
      if (end.classIII == null) {
        warn('NULL_CLASS', `End #${end.endNum || i+1}: classIII not extracted — defaulting to Class IV (0). Verify against SOI.`);
        e.drpEndorsements[i].classIII = 0; // Class IV is safer default than silently assuming Class III
      }
      if (end.classIII !== 0 && end.classIII !== 1) {
        warn('BAD_CLASS', `End #${end.endNum || i+1}: classIII value "${end.classIII}" is not 0 or 1 — re-import and check SOI Class III% column`);
      }
    });

    // Cross-check: trigger price should equal projected price × coverage level (both from same row)
    // This is a pure arithmetic check — it doesn't require knowing any external price
    e.drpEndorsements.forEach((end, i) => {
      if (end.triggerPrice != null && end.projectedPrice != null) {
        if (end.triggerPrice > end.projectedPrice)
          err('TRIGGER_EXCEEDS_PROJECTED', `End #${end.endNum || i+1}: trigger price $${end.triggerPrice} > projected price $${end.projectedPrice} — impossible, trigger = projected × coverage level so trigger must always be less`);
        if (end.coverageLevel != null && end.projectedPrice > 0) {
          const expected = end.projectedPrice * end.coverageLevel;
          const delta = Math.abs(expected - end.triggerPrice);
          if (delta > 0.10)
            warn('TRIGGER_MATH', `End #${end.endNum || i+1}: trigger $${end.triggerPrice} does not match projected $${end.projectedPrice} × coverage ${(end.coverageLevel*100).toFixed(0)}% = $${expected.toFixed(2)} — one of these three values may be wrong`);
        }
      }
    });

    // Check for duplicate endorsement numbers
    const endNums = e.drpEndorsements.map(e => e.endNum).filter(Boolean);
    const dupEnds = endNums.filter((n, i) => endNums.indexOf(n) !== i);
    if (dupEnds.length) warn('DUP_END_NUMS', `Duplicate endorsement numbers: ${[...new Set(dupEnds)].join(', ')}`);
  }

  // ── LRP CHECKS ────────────────────────────────────────────────────────────
  if (e.documentType === 'LRP' && e.lrpEndorsements?.length) {
    e.lrpEndorsements.forEach((end, i) => {
      const tag = `End ${i+1} (${end.commodity || '?'})`;

      // Head count: 1–50,000
      if (end.numberOfHead != null) {
        if (end.numberOfHead <= 0)    err('ZERO_HEAD', `${tag}: number of head is 0 or negative`);
        if (end.numberOfHead > 50000) warn('HIGH_HEAD', `${tag}: ${end.numberOfHead} head seems very high — verify`);
      } else {
        warn('NULL_HEAD', `${tag}: number of head is missing`);
      }

      // Target weight: Feeder 1–10 cwt, Fed Cattle 5–20 cwt, Calves 1–5 cwt
      if (end.targetWeight != null) {
        if (end.targetWeight <= 0 || end.targetWeight > 30)
          warn('BAD_WEIGHT', `${tag}: target weight ${end.targetWeight} cwt is outside expected range — verify units`);
      }

      // Coverage price: must be positive
      if (end.coveragePrice != null && end.coveragePrice <= 0)
        err('BAD_LRP_PRICE', `${tag}: coverage price $${end.coveragePrice} is not positive`);

      // End date must be in the future relative to sales date, and within ~1 year
      if (end.salesDate && end.endDate) {
        const sales = new Date(end.salesDate);
        const endDt = new Date(end.endDate);
        const diffDays = (endDt - sales) / (1000 * 60 * 60 * 24);
        if (diffDays < 0)   err('END_BEFORE_SALES', `${tag}: end date is before sales date`);
        if (diffDays > 400) warn('LONG_LRP', `${tag}: LRP period is ${Math.round(diffDays)} days — unusually long`);
      }
    });
  }

  // ── PP ELIGIBILITY CHECKS ─────────────────────────────────────────────────
  if (e.ppEligibility?.length) {
    e.ppEligibility.forEach(pp => {
      const tag = `${pp.county || '?'} ${pp.crop || '?'}`;
      // PP liability per acre: $50–$2000 is reasonable
      if (pp.ppLiabilityPerAcre != null && (pp.ppLiabilityPerAcre < 50 || pp.ppLiabilityPerAcre > 2000))
        warn('BAD_PP_LIABILITY', `${tag}: PP liability/acre $${pp.ppLiabilityPerAcre} is outside expected range $50–$2000`);
      // Historical acres: should be positive
      [pp.yr1, pp.yr2, pp.yr3, pp.yr4].forEach((ac, yi) => {
        if (ac != null && ac < 0) err('NEG_PP_ACRES', `${tag}: year ${yi+1} acres is negative (${ac})`);
      });
    });
  }

  return { warnings, errors, corrections, cleaned: e };
}

function buildFarmerFromExtraction(extracted) {
  const policyTypes = [];
  if (extracted.approvedYields?.length || extracted.ppEligibility?.length) policyTypes.push('crop');
  if (extracted.lrpEndorsements?.length) policyTypes.push('lrp');
  if (extracted.drpEndorsements?.length) policyTypes.push('drp');
  if (extracted.prfIntervals?.length) policyTypes.push('prf');
  if (!policyTypes.length) policyTypes.push('crop');

  // Warn if SOI_CROP has no approvedYields — likely an extraction issue
  if (extracted.documentType === 'SOI_CROP' && !extracted.approvedYields?.length) {
    console.warn('⚠️ SOI_CROP extracted with 0 approvedYields — possible extraction failure:', extracted);
  }

  // ── SOI_CROP VALIDATION ──────────────────────────────────────────────────
  // Catch common LLM extraction errors before they reach the DB.
  if (extracted.documentType === 'SOI_CROP' && extracted.approvedYields?.length > 1) {
    const yields = extracted.approvedYields;

    // 1. Duplicate acres across counties: if 2+ counties have the same non-zero acres
    //    for the same crop, that's almost certainly a copy error (real fields never match exactly).
    const acreGroups = {};
    yields.forEach(y => {
      if (!y.acres || y.acres <= 0) return;
      const key = `${y.crop}|${y.acres}`;
      acreGroups[key] = (acreGroups[key] || []);
      acreGroups[key].push(y.county);
    });
    Object.entries(acreGroups).forEach(([key, counties]) => {
      if (counties.length > 1) {
        const [crop, acres] = key.split('|');
        console.warn(`⚠️ SOI duplicate acres detected: ${counties.join(' & ')} both show ${acres} acres for ${crop}. Nulling acres on all but first county to prevent bad data.`);
        // Zero out all but the first occurrence — better to show blank than wrong data
        let first = true;
        extracted.approvedYields = extracted.approvedYields.map(y => {
          if (y.crop === crop && String(y.acres) === acres) {
            if (first) { first = false; return y; }  // keep first
            return { ...y, acres: null };             // null the rest
          }
          return y;
        });
      }
    });

    // 1b. Policy-total-acres on a single county: if one county's acres equals the sum of ALL
    //     counties' acres for the same crop, the AI likely used the policy total instead of the
    //     county-level figure (e.g. 482.71 total assigned to Livingston instead of 367.10).
    //     Null out that county's acres so the user is prompted to correct it manually.
    const cropList = [...new Set(yields.map(y => (y.crop||'').toUpperCase()))];
    cropList.forEach(crop => {
      const cropRows = yields.filter(y => (y.crop||'').toUpperCase() === crop && y.acres > 0);
      if (cropRows.length < 2) return; // need at least 2 counties to detect this
      const totalAcres = cropRows.reduce((s, y) => s + (y.acres || 0), 0);
      cropRows.forEach(y => {
        // If one county holds ≥ 90% of the total acres and there are other counties with no acres,
        // it strongly suggests the policy total was assigned to one county only.
        const otherCountiesBlank = yields.filter(r =>
          (r.crop||'').toUpperCase() === crop &&
          r.county !== y.county &&
          (!r.acres || r.acres <= 0)
        ).length;
        if (otherCountiesBlank > 0 && y.acres >= totalAcres * 0.90) {
          console.warn(`⚠️ SOI possible policy-total-acres on single county: ${y.county} has ${y.acres} ac for ${crop} but other counties show 0 — may be the policy total assigned to one county. Nulling to force manual review.`);
          extracted.approvedYields = extracted.approvedYields.map(r =>
            (r.crop||'').toUpperCase() === crop && r.county === y.county
              ? { ...r, acres: null }
              : r
          );
        }
      });
    });

    // 2. All counties have identical coverage level on a multi-county policy — warn but don't modify
    //    (could be legitimate, e.g. Callan Farms 50% across counties)
    const covLevels = [...new Set(yields.map(y => y.coverageLevel).filter(Boolean))];
    if (covLevels.length === 1 && yields.length > 1) {
      console.info(`ℹ️ SOI multi-county: all counties show same coverage level ${covLevels[0]*100}% — may be correct or may be a copy.`);
    }
  }
  // ────────────────────────────────────────────────────────────────────────

  // PP_REPORT documents must NEVER produce approvedYields — that data comes only from APH or SOI.
  // APH and SOI documents must NEVER produce ppEligibility rows.
  const rawYields = extracted.documentType === 'PP_REPORT'
    ? []  // PP reports cannot set approvedYields — only APH/SOI can
    : (extracted.approvedYields || []);

  const rawPP = extracted.documentType === 'PP_REPORT'
    ? (extracted.ppEligibility || [])
    : [];

  const ppEligibility = rawPP.map(p => {
    const acres = [p.yr1, p.yr2, p.yr3, p.yr4].filter(v => v != null && v > 0);
    const maxCalc = acres.length > 0 ? Math.max(...acres) : 0;
    const practice = normalizePractice(p.practice);
    return {
      ...p,
      practice,
      plantedAcres: '',
      maxEligAcres: p.maxEligAcres ?? maxCalc,
      aphYield:           p.aphYield          ?? null,
      coverageLevel:      p.coverageLevel     ?? null,
      ppLiabilityPerAcre: p.ppLiabilityPerAcre ?? null,
    };
  });

  const displayName = extracted.dba
    ? extracted.name + ' (' + extracted.dba + ')'
    : (extracted.name || 'Unknown');

  return {
    id: Date.now() + Math.random(),
    name: displayName,
    address: extracted.address || '',
    phone: extracted.phone || '',
    policyNumber: extracted.policyNumber || '',
    cropYear: extracted.cropYear || CURRENT_CROP_YEAR,
    county: extracted.county || '',
    state: extracted.state || 'NY',
    aip: extracted.aip || 'NAU',
    agent: extracted.agent || '',
    agency: extracted.agency || '',
    insurer: extracted.insurer || '',
    policyTypes,
    approvedYields: (() => {
      // Extraction now returns one row per unit. Average in code by county+crop+type+practice.
      // This is deterministic and never subject to LLM rounding errors.

      // Normalize a yield row — split compound practice/type like "NON IRR/W" → practice=Non-Irrigated, type=W
      function normalizeYieldRow(a) {
        let rawType = (a.type || "").toUpperCase().trim();
        let practice = normalizePractice(a.practice);
        // NAU compact SOI encodes practice+type as "NON IRR/W" in the Practice/Type column
        // The LLM may put this in either the type or practice field — handle both
        const rawPrac = (a.practice || "").toUpperCase().trim();
        const slashField = rawType.includes("/") ? rawType : rawPrac.includes("/") ? rawPrac : null;
        if (slashField) {
          const parts = slashField.split("/");
          const typePart = parts[parts.length - 1].trim();     // e.g. "W" or "SRW"
          const pracPart = parts.slice(0, -1).join("/").trim(); // e.g. "NON IRR"
          rawType = typePart;
          if (!practice || practice === "") practice = normalizePractice(pracPart);
          else if (pracPart) practice = normalizePractice(pracPart); // prefer explicit
        }
        return {
          ...a,
          county:   (a.county || "").toUpperCase().trim(),
          crop:     (a.crop   || "").toUpperCase().trim(),
          type:     rawType,
          practice,
        };
      }

      const normalized = rawYields.filter(a => a.county && a.crop).map(normalizeYieldRow);

      // Group by county+crop+type+practice
      const groups = {};
      normalized.forEach(a => {
        const key = `${a.county}|${a.crop}|${a.type}|${a.practice}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(a);
      });

      // Average approvedYield within each group, keep all other fields from first row
      // For coverageLevel: take from any non-null row (never average — it's a policy-level election)
      return Object.values(groups).map(rows => {
        const validYields = rows.map(r => r.approvedYield).filter(y => y != null && y > 0);
        const avgYield = validYields.length > 0
          ? Math.round(validYields.reduce((s,y) => s+y, 0) / validYields.length)
          : null;
        const coverageLevel = (rows.find(r => r.coverageLevel != null) || rows[0]).coverageLevel;
        return {
          ...rows[0],
          approvedYield: avgYield,
          coverageLevel,
          unitCount: rows.length,
        };
      });
    })(),
    ppEligibility,
    // SCO/ECO endorsements stored as special crop_coverage rows (plan='SCO-RP' or 'ECO-RP')
    // They are NOT averaged (one row per county+crop+plan) and are kept separate from base RP rows
    scoEcoEndorsements: (extracted.scoEcoEndorsements || []).map(e => ({
      county:        (e.county||"").toUpperCase().trim(),
      crop:          (e.crop||"").toUpperCase().trim(),
      type:          (e.type||"").toUpperCase().trim(),
      practice:      normalizePractice(e.practice),
      plan:          e.plan || "SCO-RP",
      coverageLevel: e.coverageLevel ?? null,
      acres:         e.acres ?? null,
      approvedYield: null,
      unitCount:     1,
    })),
    lrpPolicies: extracted.lrpEndorsements?.length ? [{
      id: 'lrp-' + Date.now(),
      commodity: extracted.lrpEndorsements[0]?.commodity || 'Cattle',
      type: extracted.lrpEndorsements[0]?.type || '',
      county: extracted.lrpEndorsements[0]?.county || '',
      endorsements: extracted.lrpEndorsements.map(e => ({
        lineId: e.lineId ?? e.line_id ?? null,
        commodity: e.commodity || 'Cattle',
        type: e.type || '',
        county: e.county || '',
        salesDate: e.salesDate || e.sales_date || null,
        endDate: e.endDate || e.end_date || null,
        weeksOut: e.weeksOut ?? e.endorsementLength ?? e.weeks_out ?? null,
        head: e.head ?? e.numberOfHead ?? e.number_of_head ?? null,
        targetWeight: e.targetWeight ?? e.target_weight ?? null,
        coveragePrice: e.coveragePrice ?? e.coverage_price ?? null,
        insuredValue: e.insuredValue ?? e.insured_value ?? null,
        premium: e.premium ?? e.totalPremium ?? e.total_premium ?? null,
        producerPremium: e.producerPremium ?? e.netPremium ?? e.net_premium ?? e.producer_premium ?? null,
        subsidy: e.subsidy ?? null,
        billingDate: e.billingDate || e.billing_date || null,
        actualEndValue: e.actualEndValue ?? e.actual_end_value ?? 0,
        payableIndemnity: e.payableIndemnity ?? e.payable_indemnity ?? null,
      })),
    }] : [],
    drpPolicies: extracted.drpEndorsements?.length ? [{
      id: 'drp-' + Date.now(),
      commodity: 'Milk',
      county: '',
      totalDeclaredMilk: extracted.drpEndorsements.reduce((s,e) => s+(e.declaredMilkProd||0), 0),
      totalInsuredValue: extracted.drpEndorsements.reduce((s,e) => s+(e.expRevGuar||0), 0),
      totalPremium:      extracted.drpEndorsements.reduce((s,e) => s+(e.totalPremium||0), 0),
      subsidyAmount:     extracted.drpEndorsements.reduce((s,e) => s+(e.subsidy||0), 0),
      producerPremium:   extracted.drpEndorsements.reduce((s,e) => s+(e.producerPremium||0), 0),
      endorsements: extracted.drpEndorsements,
    }] : [],
    prfPolicies: (() => {
      if (!extracted.prfIntervals?.length) return [];
      
      // SINGLE POLICY — one banner for the whole PRF SOI.
      // Grid grouping (gridId + county) handles multi-county display.
      // prfCoverageRows are used to compute accurate totals across all counties/types.
      
      // Sum coverage rows for policy-level totals (more accurate than header-level fields
      // which the LLM sometimes gets wrong for multi-county SOIs)
      let covTotalAcres = 0, covTotalLiab = 0, covTotalPrem = 0, covTotalSub = 0, covTotalNet = 0;
      let covLevel = null, covPF = null, covBillDate = null;
      if (extracted.prfCoverageRows?.length) {
        extracted.prfCoverageRows.forEach(cr => {
          covTotalAcres += Number(cr.acres) || 0;
          covTotalLiab  += Number(cr.liability) || 0;
          covTotalPrem  += Number(cr.premium) || 0;
          covTotalSub   += Number(cr.subsidy) || 0;
          covTotalNet   += Number(cr.netPremium) || 0;
          covLevel   = covLevel   ?? cr.coverageLevel;
          covPF      = covPF      ?? cr.productivityFactor;
          covBillDate = covBillDate ?? cr.billingDate;
        });
      }

      // Compute interval sums as fallback
      const intervalPremSum = extracted.prfIntervals.reduce((s,e) => s+(Number(e.premium)||0), 0);
      const intervalLiabSum = extracted.prfIntervals.reduce((s,e) => s+(Number(e.liability)||0), 0);

      // Total acres: prefer COVERAGE table sum > header-level > interval-derived
      const totalAcres = covTotalAcres > 0 ? covTotalAcres
        : (extracted.prfTotalAcres > 0 ? extracted.prfTotalAcres : 0);

      // Gross premium: prefer COVERAGE sum > header-level > interval sum + subsidy
      const subsidy = covTotalSub > 0 ? covTotalSub : (extracted.prfSubsidy ?? null);
      const grossPrem = covTotalPrem > 0 ? covTotalPrem
        : (extracted.prfGrossPremium > 0 ? extracted.prfGrossPremium
          : (subsidy > 0 ? intervalPremSum + subsidy : intervalPremSum));

      const netPrem = covTotalNet > 0 ? covTotalNet
        : (extracted.prfNetPremium ?? intervalPremSum);

      // Determine primary county (first county in COVERAGE table or extraction)
      const primaryCounty = extracted.prfCoverageRows?.[0]?.county
        || extracted.county
        || extracted.prfIntervals[0]?.county
        || '';

      return [{
        id: 'prf-' + Date.now(),
        policyNumber: extracted.policyNumber || '',
        cropYear: extracted.cropYear,
        county: primaryCounty,
        coverageLevel: covLevel ?? extracted.prfCoverageLevel ?? extracted.prfIntervals[0]?.coverageLevel ?? null,
        productivityFactor: covPF ?? extracted.prfIntervals[0]?.productivityFactor ?? null,
        totalAcres: totalAcres,
        totalLiability: covTotalLiab > 0 ? covTotalLiab : intervalLiabSum,
        totalPremium: grossPrem,
        netPremium: netPrem,
        subsidy: subsidy,
        billingDate: covBillDate ?? extracted.prfBillingDate ?? null,
        intervals: extracted.prfIntervals,
      }];
    })(),
  };
}

function mergeFarmers(base, incoming) {
  // Merge approvedYields: update existing rows with newer APH data, add new county/crop combos.
  // A row matches on county + crop. If the incoming row has a better (non-null) approvedYield,
  // update that field — but never touch the shape of ppEligibility rows.
  const yieldKey = (r) => `${(r.county||"").toUpperCase().trim()}|${(r.crop||"").toUpperCase()}|${(r.type||"").toUpperCase()}|${normalizePractice(r.practice).toUpperCase()}`;
  const mergedYields = base.approvedYields.map(b => {
    const inc = incoming.approvedYields.find(i => yieldKey(i) === yieldKey(b));
    if (!inc) return b;
    // Incoming always wins — SOI and APH docs are authoritative sources
    return {
      ...b,
      approvedYield:  inc.approvedYield  != null ? inc.approvedYield  : b.approvedYield,
      coverageLevel:  inc.coverageLevel  != null ? inc.coverageLevel  : b.coverageLevel,
      plan:           inc.plan           || b.plan,
      unitCount:      inc.unitCount      != null ? inc.unitCount      : (b.unitCount ?? 1),
      acres:          inc.acres          != null ? inc.acres          : b.acres,
    };
  });
  // Add any new county/crop/type/practice combos that aren't in base yet
  const newYields = incoming.approvedYields.filter(inc =>
    !base.approvedYields.some(b => yieldKey(b) === yieldKey(inc))
  );

  // Merge ppEligibility: incoming PP rows update existing ones (newer data wins),
  // new county+crop combos are added. APH/SOI docs always return ppEligibility:[]
  // so uploading an APH or SOI will NEVER wipe out PP eligibility data.
  const ppKey = (r) => `${(r.county||"").toUpperCase().trim()}|${(r.crop||"").toUpperCase()}|${(r.type||"").toUpperCase()}|${normalizePractice(r.practice).toUpperCase()}`;
  const mergedPP = [
    // Update existing PP rows — key includes type so GSG/GSS/NTS rows coexist
    ...base.ppEligibility.map(b => {
      const inc = incoming.ppEligibility.find(i => ppKey(i) === ppKey(b));
      if (!inc) return b;
      return { ...b, ...Object.fromEntries(Object.entries(inc).filter(([, v]) => v != null)) };
    }),
    // Add brand-new county+crop+type combos not previously seen
    ...incoming.ppEligibility.filter(inc =>
      !base.ppEligibility.some(b => ppKey(b) === ppKey(inc))
    ),
  ];

  return {
    ...base,
    name:      base.name      || incoming.name,
    address:   base.address   || incoming.address,
    phone:     base.phone     || incoming.phone,
    agent:     base.agent     || incoming.agent,
    agency:    base.agency    || incoming.agency,
    insurer:   base.insurer   || incoming.insurer,
    approvedYields: [...mergedYields, ...newYields],
    ppEligibility:  mergedPP,
    // SCO/ECO: incoming rows replace base rows for matching county+crop+type+plan combos
    scoEcoEndorsements: (() => {
      const scoKey = (e) => `${(e.county||"").toUpperCase().trim()}|${(e.crop||"").toUpperCase()}|${(e.type||"").toUpperCase()}|${(e.plan||"").toUpperCase()}`;
      const incomingSco = incoming.scoEcoEndorsements || [];
      const baseSco     = base.scoEcoEndorsements     || [];
      if (!incomingSco.length) return baseSco;
      const merged = baseSco.map(b => {
        const inc = incomingSco.find(i => scoKey(i) === scoKey(b));
        return inc ? { ...b, ...inc } : b;
      });
      const newSco = incomingSco.filter(inc => !baseSco.some(b => scoKey(b) === scoKey(inc)));
      return [...merged, ...newSco];
    })(),
    // Carry forward corrections from the incoming upload — those are the new rows needing review
    _valCorrections: incoming._valCorrections?.length ? incoming._valCorrections : (base._valCorrections || []),
    lrpPolicies: (() => {
      if (!incoming.lrpPolicies?.length) return base.lrpPolicies || [];
      if (!base.lrpPolicies?.length)     return incoming.lrpPolicies;
      // Merge LRP endorsements — incoming fills in missing fields on matching endorsements
      const baseEnds    = base.lrpPolicies.flatMap(p => p.endorsements || []);
      const incomingEnds = incoming.lrpPolicies.flatMap(p => p.endorsements || []);
      const combined = [...baseEnds];
      incomingEnds.forEach(ne => {
        const dupeIdx = combined.findIndex(e =>
          (e.salesDate === ne.salesDate && e.endDate === ne.endDate && e.coveragePrice === ne.coveragePrice) ||
          (e.salesDate === ne.salesDate && e.endDate === ne.endDate && e.head === ne.head && e.head != null) ||
          (e.endDate === ne.endDate && e.coveragePrice === ne.coveragePrice && e.head === ne.head && e.endDate != null)
        );
        if (dupeIdx >= 0) {
          const old = combined[dupeIdx];
          const merged = {};
          const allKeys = new Set([...Object.keys(old), ...Object.keys(ne)]);
          allKeys.forEach(k => {
            if (k === 'actualEndValue') {
              merged[k] = (old[k] && old[k] > 0) ? old[k] : (ne[k] || 0);
            } else if (k === 'payableIndemnity') {
              merged[k] = (old[k] && old[k] > 0) ? old[k] : (ne[k] || null);
            } else {
              merged[k] = (ne[k] != null && ne[k] !== 0) ? ne[k] : (old[k] ?? ne[k]);
            }
          });
          combined[dupeIdx] = merged;
        } else {
          combined.push(ne);
        }
      });
      return [{
        ...(base.lrpPolicies[0] || {}),
        ...(incoming.lrpPolicies[0] || {}),
        endorsements: combined,
      }];
    })(),
    drpPolicies: (() => {
      if (!incoming.drpPolicies.length) return base.drpPolicies;
      if (!base.drpPolicies.length)     return incoming.drpPolicies;
      const baseMeta = base.drpPolicies[0] || {};
      const incomingMeta = incoming.drpPolicies[0] || {};
      // Merge all DRP endorsements — incoming wins on collision.
      // Key on quarter+endNum (more stable than endDate which can vary in format).
      // If incoming has the same quarter count as base, treat as a full replacement.
      const incomingEnds = incoming.drpPolicies.flatMap(p => p.endorsements || []);
      const baseEnds     = base.drpPolicies.flatMap(p => p.endorsements || []);
      // If incoming covers the same or more quarters than base, it's a full re-upload — replace entirely
      const incomingQuarters = new Set(incomingEnds.map(e => e.quarter).filter(Boolean));
      const baseQuarters     = new Set(baseEnds.map(e => e.quarter).filter(Boolean));
      const incomingCoversBase = [...baseQuarters].every(q => incomingQuarters.has(q));
      let allEnds;
      if (incomingCoversBase) {
        // Full replacement — incoming is authoritative for all quarters it covers
        allEnds = incomingEnds;
      } else {
        // Partial upload — union, incoming wins on collision per quarter+endNum
        const endMap = {};
        baseEnds.forEach(e => { endMap[`${e.quarter}|${e.endNum}`] = e; });
        incomingEnds.forEach(e => { endMap[`${e.quarter}|${e.endNum}`] = e; });
        allEnds = Object.values(endMap);
      }
      allEnds = allEnds.sort((a, b) => {
        const da = new Date(a.endDate || ''), db = new Date(b.endDate || '');
        return (isNaN(da) ? 0 : da) - (isNaN(db) ? 0 : db);
      });
      return [{
        ...baseMeta,
        ...incomingMeta,
        endorsements: allEnds,
        totalDeclaredMilk: allEnds.reduce((s, e) => s + (Number(e.declaredMilkProd)||0), 0),
        totalInsuredValue: allEnds.reduce((s, e) => s + (Number(e.expRevGuar)||0), 0),
        totalPremium:      allEnds.reduce((s, e) => s + (Number(e.totalPremium)||0), 0),
        subsidyAmount:     allEnds.reduce((s, e) => s + (Number(e.subsidy)||0), 0),
        producerPremium:   allEnds.reduce((s, e) => s + (Number(e.producerPremium)||0), 0),
      }];
    })(),
    prfPolicies: incoming.prfPolicies?.length ? incoming.prfPolicies : (base.prfPolicies || []),
    policyTypes: [...new Set([...base.policyTypes, ...incoming.policyTypes])],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPORT SCREEN
// ─────────────────────────────────────────────────────────────────────────────
// ── JSZIP LOADER ─────────────────────────────────────────────────────────────
async function loadJSZip() {
  if (window.JSZip) return window.JSZip;
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    s.onload = () => resolve(window.JSZip);
    s.onerror = () => reject(new Error('Failed to load JSZip'));
    document.head.appendChild(s);
  });
}

// Extract all PDFs from a zip file, return array of File objects
async function extractPDFsFromZip(zipFile) {
  const JSZip = await loadJSZip();
  const zip = await JSZip.loadAsync(zipFile);
  const pdfFiles = [];
  const promises = [];
  zip.forEach((relativePath, zipEntry) => {
    if (zipEntry.dir) return;
    const name = relativePath.split('/').pop();
    if (!name.toLowerCase().endsWith('.pdf')) return;
    promises.push(
      zipEntry.async('blob').then(blob => {
        const file = new File([blob], name, { type: 'application/pdf' });
        pdfFiles.push(file);
      })
    );
  });
  await Promise.all(promises);
  // Sort alphabetically for predictable order
  pdfFiles.sort((a, b) => a.name.localeCompare(b.name));
  return pdfFiles;
}

// ── THE ORIGINAL BEHIND A BOOK IMPORT IS KEPT ───────────────────────────────
// The Import screen read each PDF with AI, saved the rows and threw the PDF away, so
// a Schedule of Insurance or APH database typed into the book had no original to be
// checked against. Each file is now stored ONCE at the canonical path
// (src/documentIndex.js) with its SHA-256 and indexed by one farmer_documents row —
// BEFORE the book save, and NEVER in a way that can stop or change that save: every
// failure here is caught and printed on the result screen. Same bytes already on file
// for the farm → the stored copy is reused, nothing is written.
async function readDocumentIndex(filter) {
  const res = await authedFetch(`${SUPABASE_URL}/rest/v1/farmer_documents?select=id,farmer_id,storage_path&${filter}&limit=1`);
  if (!res.ok) throw new Error(`Supabase ${res.status}: ${(await res.text().catch(() => "")).slice(0, 300)}`);
  const rows = await res.json();
  return (Array.isArray(rows) && rows[0]) || null;
}
async function storeImportOriginal(orig) {
  try {
    let sha = null;
    try { sha = await fileSha256(orig.file); } catch { sha = null; }
    // The farm, if the book already holds this policy year. A first SOI for a new grower
    // has none yet; its row is given the farmer once the save names it.
    // A caller that already KNOWS the farm (the FSA-156EZ uploader, which is opened on one
    // farmer) passes it; the policy lookup is for an import that has to discover it.
    let farmerId = orig.farmerId || null;
    if (!farmerId && orig.policyNumber && orig.cropYear) {
      const pol = await sbFetch("policies", { select: "farmer_id", filters: [`policy_number=eq.${encodeURIComponent(orig.policyNumber)}`, `crop_year=eq.${orig.cropYear}`] });
      const ids = [...new Set((pol || []).map((p) => p.farmer_id).filter(Boolean))];
      if (ids.length === 1) farmerId = ids[0];
    }
    const byHash = DOCINDEX.sameDocumentFilter(farmerId, sha);
    let prior = null;
    if (byHash) {
      try { prior = await readDocumentIndex(byHash); }
      catch (e) {
        if (!DOCINDEX.isMissingIndexColumn(e)) throw e;
        prior = await readDocumentIndex(DOCINDEX.samePathFilter(farmerId, sha, orig.file.name));
      }
    }
    if (prior) return { original: orig, status: "reused", id: prior.id, path: prior.storage_path, farmerId: prior.farmer_id || null, sha };
    const path = DOCINDEX.documentStoragePath(farmerId, orig.cropYear, orig.docType, sha, orig.file.name);
    await sbStorageUpload("farmer-documents", path, orig.file);
    const row = DOCINDEX.indexRow({
      farmerId, policyNumber: orig.policyNumber, docType: orig.docType, cropYear: orig.cropYear,
      fileName: orig.file.name, source: "pdf_import", storagePath: path, sha256: sha,
      parseStatus: "extracted",
      notes: orig.docType === "other" && orig.documentType ? `extractor documentType: ${orig.documentType}` : (orig.farmerCount > 1 ? `one file, ${orig.farmerCount} farms` : null),
    });
    let ins;
    try { ins = await sbInsert("farmer_documents", [row]); }
    catch (e) {
      if (!DOCINDEX.isMissingIndexColumn(e)) throw e;          // sql/103 not applied yet
      ins = await sbInsert("farmer_documents", [DOCINDEX.withoutIndexColumns(row)]);
    }
    return { original: orig, status: "stored", id: (Array.isArray(ins) && ins[0] && ins[0].id) || null, path, farmerId, sha };
  } catch (e) {
    return { original: orig, status: "failed", error: e.message };
  }
}
async function storeImportOriginals(results) {
  const out = [];
  for (const o of DOCINDEX.originalsFromResults(results)) out.push(await storeImportOriginal(o));
  return out;
}
// After the save: an original filed before its farm existed is given the farmer the
// save returned. A failure here leaves the original stored and unattached — reported.
async function attachImportOriginals(stored, saveResults) {
  for (const s of stored) {
    if (s.status === "failed" || s.farmerId || !s.id) continue;
    const fid = DOCINDEX.savedFarmerIdFor(s.original, saveResults);
    if (!fid) continue;
    try {
      const res = await authedFetch(`${SUPABASE_URL}/rest/v1/farmer_documents?id=eq.${s.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json", Prefer: "return=minimal" }, body: JSON.stringify({ farmer_id: fid }),
      });
      if (!res.ok) throw new Error(`Supabase ${res.status}`);
      s.farmerId = fid;
    } catch (e) { s.attachError = e.message; }
  }
  return stored;
}
function ImportOriginalsNote({ stored }) {
  if (!stored || !stored.length) return null;
  const n = (st) => stored.filter((s) => s.status === st).length;
  const failed = stored.filter((s) => s.status === "failed");
  const loose = stored.filter((s) => s.status !== "failed" && !s.farmerId);
  return (
    <div style={{ marginBottom:16, padding:'10px 16px', borderRadius:10, background: failed.length ? '#fdf0e0' : '#f4f8f4', border:`1px solid ${failed.length ? '#f0d6b0' : '#dde8d8'}`, fontSize:12, color:'#1a2e1a' }}>
      <strong>Original PDFs:</strong> {n("stored")} stored · {n("reused")} already on file{failed.length ? ` · ${failed.length} NOT stored` : ''}{loose.length ? ` · ${loose.length} not linked to a farm` : ''}
      {failed.map((s, i) => <div key={i} style={{ color:'#8a3a00', marginTop:3 }}>✗ {s.original?.file?.name}: {s.error} — the farm data above was saved regardless.</div>)}
    </div>
  );
}

function ImportScreen({ farmers, onImport }) {
  const [files, setFiles]                 = useState([]);
  const [statuses, setStatuses]           = useState({});
  const [phase, setPhase]                 = useState('upload');
  const [mergedFarmers, setMergedFarmers] = useState([]);
  const [conflicts, setConflicts]         = useState([]);
  const [conflictIdx, setConflictIdx]     = useState(0);
  const [resolved, setResolved]           = useState([]);
  const [isDragging, setIsDragging]       = useState(false);
  const [csvText, setCsvText]             = useState('');
  const [csvStatus, setCsvStatus]         = useState(null);
  const [lrpFile,      setLrpFile]      = useState(null);
  const [lrpStatus,    setLrpStatus]    = useState(null);
  const [lrpIndFile,   setLrpIndFile]   = useState(null);
  const [lrpIndStatus, setLrpIndStatus] = useState(null);
  const [lrpEndFile,   setLrpEndFile]   = useState(null);
  const [lrpEndStatus, setLrpEndStatus] = useState(null);
  const [cropXlsxFile,   setCropXlsxFile]   = useState(null);
  const [cropXlsxStatus, setCropXlsxStatus] = useState(null);
  const [cropXlsxPreview, setCropXlsxPreview] = useState(null);
  const [cropXlsxMapping, setCropXlsxMapping] = useState({});
  const [auditFile,    setAuditFile]    = useState(null);
  const [auditStatus,  setAuditStatus]  = useState(null);
  const [auditPreview, setAuditPreview] = useState(null);
  const [auditMapping, setAuditMapping] = useState({});
  const [auditReport,  setAuditReport]  = useState(null);
  const [auditExpanded, setAuditExpanded] = useState({});
  const [auditType, setAuditType] = useState('crop');
  const [auditCropFilter, setAuditCropFilter] = useState({});
  const [histFile,    setHistFile]    = useState(null);
  const [histStatus,  setHistStatus]  = useState(null);
  const [histPreview, setHistPreview] = useState(null);
  const [histMapping, setHistMapping] = useState({});
  const [histYear, setHistYear] = useState('2025');
  const [histFormat, setHistFormat] = useState(null); // 'NAU' | 'RH' | null
  const [rmaFile,   setRmaFile]   = useState(null);
  const [rmaStatus, setRmaStatus] = useState(null);
  const [activeTab, setActiveTab]         = useState('pdf');
  const [xlsxFile,        setXlsxFile]        = useState(null);
  const [xlsxFile2,       setXlsxFile2]       = useState(null); // second file (NAU+RH pair)
  const [xlsxDetected,    setXlsxDetected]    = useState(null); // detection result
  const [xlsxStatus,      setXlsxStatus]      = useState(null);
  const [xlsxPreview,     setXlsxPreview]     = useState(null);
  const [xlsxImporting,   setXlsxImporting]   = useState(false);
  const [saveStatus, setSaveStatus]       = useState(null);
  const [saveError, setSaveError]         = useState(null);
  const [saveResults, setSaveResults]     = useState([]);
  const [originalsReport, setOriginalsReport] = useState(null);
  const [zipBatch, setZipBatch]           = useState(null);
  // typeOverrides: map of "policyNumber|cropYear|arrayName|idx" → 'GSG' | 'GSS'
  // Populated when user manually confirms ambiguous corn type on import review screen
  const [typeOverrides, setTypeOverrides] = useState({});
  const currentFiles = useRef([]);
  const extractionResults = useRef([]);

  function setStatus(name, status, result=null, confidence=null, note='') {
    setStatuses(prev => ({ ...prev, [name]: { status, result, confidence, note } }));
  }
  function handleDrop(e) {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const all = Array.from(e.dataTransfer.files);
    const zips = all.filter(f => f.name.toLowerCase().endsWith('.zip'));
    const pdfs = all.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (zips.length > 0) { handleZipFile(zips[0]); return; }
    if (pdfs.length) addFiles(pdfs);
  }
  function handleDragOver(e) { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }
  function handleFileInput(e) {
    const all = Array.from(e.target.files);
    const zips = all.filter(f => f.name.toLowerCase().endsWith('.zip'));
    const pdfs = all.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (zips.length > 0) { handleZipFile(zips[0]); }
    else { addFiles(pdfs); }
    e.target.value = '';
  }
  function addFiles(newFiles) {
    const deduped = newFiles.filter(f => !currentFiles.current.find(x => x.name === f.name));
    if (!deduped.length) return;
    currentFiles.current = [...currentFiles.current, ...deduped];
    setFiles([...currentFiles.current]);
    deduped.forEach(f => setStatus(f.name, 'waiting'));
  }
  function removeFile(name) {
    currentFiles.current = currentFiles.current.filter(f => f.name !== name);
    setFiles([...currentFiles.current]);
    setStatuses(prev => { const n = {...prev}; delete n[name]; return n; });
  }

  async function handleZipFile(zipFile) {
    setZipBatch({ zipName: zipFile.name, total: 0, phase: 'extracting', log: [], overwriteLog: [], done: 0, failed: 0, saved: 0 });
    let pdfs = [];
    try {
      pdfs = await extractPDFsFromZip(zipFile);
    } catch(err) {
      setZipBatch(b => ({ ...b, phase: 'error', errorMsg: 'Failed to read zip: ' + err.message }));
      return;
    }
    if (!pdfs.length) {
      setZipBatch(b => ({ ...b, phase: 'error', errorMsg: 'No PDF files found inside the zip.' }));
      return;
    }
    const total = pdfs.length;
    setZipBatch(b => ({ ...b, total, phase: 'processing' }));

    // Process with concurrency of 2 to stay within API rate limits
    const CONCURRENCY = 2;
    const allResults = [];
    let nextIdx = 0;
    let completed = 0;

    async function worker() {
      while (true) {
        const idx = nextIdx++;
        if (idx >= total) break;
        const file = pdfs[idx];
        let farmer = null, docType = null, errMsg = null;
        // Up to 3 attempts with backoff on rate limit
        for (let attempt = 0; attempt < 4; attempt++) {
          try {
            if (attempt > 0) {
              const wait = attempt * 30000; // 30s, 60s, 90s
              await new Promise(r => setTimeout(r, wait));
            }
            const data = await extractPDFData(file);
            // FSA-578s belong in 578 Management, not the policy import flow.
            // Importing them here creates phantom farmers + bogus "FSA-578" aip='OTHER' policies.
            if (/FSA[\s_-]?578/i.test(String(data.documentType || ""))) {
              throw new Error("This is an FSA-578. Upload it via 📋 578 Management (left nav) instead.");
            }
            farmer = buildFarmerFromExtraction(data);
            docType = data.documentType;
            break;
          } catch(err) {
            errMsg = err.message;
            const isRate = err.message?.toLowerCase().includes('rate limit')
                        || err.message?.toLowerCase().includes('overloaded')
                        || err.message?.toLowerCase().includes('exceed your organ')
                        || err.message?.toLowerCase().includes('529')
                        || err.message?.toLowerCase().includes('too many');
            if (!isRate) break;
          }
        }
        completed++;
        const logEntry = farmer
          ? { name: farmer.name || file.name, file: file.name, ok: true, docType }
          : { name: file.name, file: file.name, ok: false, error: errMsg };
        allResults.push({ file, farmer, docType, errMsg });
        setZipBatch(b => ({
          ...b,
          done: completed,
          log: [...(b.log||[]), logEntry].slice(-80), // keep last 80 in display
        }));
        // Stagger requests to stay under rate limits
        await new Promise(r => setTimeout(r, 1500));
      }
    }

    // Launch CONCURRENCY workers in parallel
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));

    // ── MERGE all extracted farmers ───────────────────────────────────────
    setZipBatch(b => ({ ...b, phase: 'merging' }));
    const farmerMap = {};
    allResults.filter(r => r.farmer).forEach(r => {
      const isDRP = r.docType === 'SOI_DRP';
      const key = isDRP
        ? ('drp_' + (r.farmer.name||'unknown').trim().toLowerCase())
        : ((r.farmer.policyNumber||r.farmer.name||'unknown') + '_' + r.farmer.cropYear);
      if (!farmerMap[key]) farmerMap[key] = { ...r, farmer: { ...r.farmer } };
      else { farmerMap[key].farmer = mergeFarmers(farmerMap[key].farmer, r.farmer); }
    });

    const allMerged = Object.values(farmerMap).map(r => r.farmer);

    // Auto-merge everything — no conflict dialogs for batch
    // Track overwrites for the post-batch summary
    const overwriteLog = [];
    const normName = s => (s||'').trim().toLowerCase();
    const isDRPOnly = f => f.drpPolicies?.length > 0 && !f.approvedYields?.length && !f.ppEligibility?.length;
    const isLRPOnly = f => f.lrpPolicies?.length > 0 && !f.approvedYields?.length && !f.ppEligibility?.length && !f.drpPolicies?.length;
    const isPRFOnly = f => f.prfPolicies?.length > 0 && !f.approvedYields?.length && !f.ppEligibility?.length && !f.drpPolicies?.length && !f.lrpPolicies?.length;
    const findExisting = f => (isDRPOnly(f) || isLRPOnly(f) || isPRFOnly(f))
      ? farmers.find(ef => normName(ef.name) === normName(f.name))
      : farmers.find(ef => ef.policyNumber === f.policyNumber && ef.cropYear === f.cropYear);

    const finalFarmers = allMerged.map(incoming => {
      const existing = findExisting(incoming);
      if (existing) {
        overwriteLog.push({ name: incoming.name, policyNumber: incoming.policyNumber });
        return mergeFarmers(existing, incoming);
      }
      return incoming;
    });

    setZipBatch(b => ({ ...b, phase: 'saving', overwriteLog }));

    // ── ORIGINALS first, then SAVE to Supabase ───────────────────────────
    // Never throws; a PDF that could not be stored is named on the result panel.
    const zipOriginals = await storeImportOriginals(allResults);
    let saved = 0; const saveErrors = []; const zipSaveResults = [];
    for (const farmer of finalFarmers) {
      try {
        const { farmerId: savedFarmerId } = await saveFarmerToSupabase(farmer);
        saved++;
        zipSaveResults.push({ name: farmer.name, policyNumber: farmer.policyNumber, cropYear: farmer.cropYear, farmerId: savedFarmerId, ok: true });
      } catch(err) {
        saveErrors.push({ name: farmer.name, error: err.message });
        zipSaveResults.push({ name: farmer.name, policyNumber: farmer.policyNumber, cropYear: farmer.cropYear, ok: false, error: err.message });
      }
    }
    // ── IMPORT to app state: ONLY the farms that saved ───────────────────
    const zipSavedFarms = DOCINDEX.farmersThatSaved(finalFarmers, zipSaveResults);
    if (zipSavedFarms.length) onImport(zipSavedFarms, true);
    setOriginalsReport(await attachImportOriginals(zipOriginals, zipSaveResults));

    const failed = allResults.filter(r => !r.farmer).length;
    const failedFiles = allResults.filter(r => !r.farmer).map(r => r.file);
    setZipBatch(b => ({
      ...b,
      phase: 'done',
      saved,
      failed,
      failedFiles,
      saveErrors,
      overwriteLog,
      log: b.log,
    }));
  }

  async function processAll() {
    const toProcess = [...currentFiles.current];
    if (!toProcess.length) return;
    setPhase('processing');
    const results = [];
    for (let i = 0; i < toProcess.length; i++) {
      const file = toProcess[i];
      setStatus(file.name, file.size > SIZE_LIMIT_BYTES ? 'compressing' : 'extracting');
      let data = null, lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          if (attempt > 0) {
            const wait = attempt * 20000;
            setStatus(file.name, 'extracting', `Rate limited — waiting ${wait/1000}s...`);
            await new Promise(r => setTimeout(r, wait));
          }
          setStatus(file.name, 'extracting');
          data = await extractPDFData(file);
          lastErr = null; break;
        } catch(err) {
          lastErr = err;
          const isRateLimit = err.message?.toLowerCase().includes('rate limit') || err.message?.toLowerCase().includes('overloaded');
          if (!isRateLimit) break;
        }
      }
      if (lastErr) {
        setStatus(file.name, 'error', lastErr.message);
        results.push({ file, farmer: null, error: lastErr.message });
      } else if (/FSA[\s_-]?578/i.test(String(data?.documentType || ""))) {
        // FSA-578s belong in 578 Management. Reject here so we don't create a phantom farmer.
        const msg = "This is an FSA-578. Upload it via 📋 578 Management (left nav) instead.";
        setStatus(file.name, 'error', msg);
        results.push({ file, farmer: null, error: msg });
      } else {
        try {
          // Run validation on raw extracted data before building farmer object
          const { warnings: valWarnings, errors: valErrors, corrections: valCorrections, cleaned: cleanedData } = validateExtraction(data);
          if (valErrors.length)   console.warn('🚨 Extraction errors:', valErrors.map(e => e.msg).join(' | '));
          if (valWarnings.length) console.info('⚠️ Extraction warnings:', valWarnings.map(w => w.msg).join(' | '));
          if (valCorrections?.length) console.info('🔧 Type corrections needed:', valCorrections.map(c => `${c.county} ${c.crop}/${c.resolvedType}: ${c.reason}`).join(' | '));

          // BATCH_PP: expand array of farmers into individual results
          if (cleanedData.documentType === 'BATCH_PP' && cleanedData.farmers?.length) {
            const batchFarmers = cleanedData.farmers.map(f => buildFarmerFromExtraction({
              ...f,
              documentType: 'PP_REPORT', // treat each as a PP_REPORT for buildFarmerFromExtraction
              aip: f.aip || cleanedData.aip || 'RAIN_AND_HAIL',
              cropYear: f.cropYear || cleanedData.cropYear || CURRENT_CROP_YEAR,
            }));
            setStatus(file.name, 'done', batchFarmers[0], 90, ` · ${batchFarmers.length} farmers extracted`);
            batchFarmers.forEach(farmer => {
              results.push({ file, farmer, docType: 'BATCH_PP', confidence: 90, valWarnings: [], valErrors: [] });
            });
          } else {
          const farmer = buildFarmerFromExtraction(cleanedData);

          // ── CROSS-DOCUMENT YIELD DISAGREEMENT CHECK ──────────────────────────
          // Compare incoming yields against existing data for the same farmer.
          // Flag significant discrepancies so the user can decide which is correct.
          if (farmer.approvedYields?.length && farmer.name) {
            const normName = farmer.name.trim().toLowerCase();
            const existingFarmer = farmers.find(f => f.name?.trim().toLowerCase() === normName);
            if (existingFarmer?.approvedYields?.length) {
              farmer.approvedYields.forEach(incoming => {
                const match = existingFarmer.approvedYields.find(ex =>
                  (ex.county||"").toUpperCase().trim() === (incoming.county||"").toUpperCase().trim() &&
                  (ex.crop||"").toUpperCase() === (incoming.crop||"").toUpperCase() &&
                  (ex.type||"").toUpperCase() === (incoming.type||"").toUpperCase() &&
                  normalizePractice(ex.practice) === normalizePractice(incoming.practice)
                );
                if (match && match.approvedYield != null && incoming.approvedYield != null) {
                  const diff = Math.abs(match.approvedYield - incoming.approvedYield);
                  if (diff > 2) {
                    valWarnings.push({
                      code: 'YIELD_DISAGREE',
                      msg: `${incoming.county} ${incoming.crop}/${incoming.type}${normalizePractice(incoming.practice)==="Organic"?" Organic":""}: incoming yield ${incoming.approvedYield} bu differs from existing ${match.approvedYield} bu (Δ${diff}). This import will overwrite the existing value.`
                    });
                  }
                }
              });
            }
          }

          // Attach valCorrections directly to the farmer object so they survive merge/conflict resolution.
          // Re-index each correction against the farmer's actual arrays right now — before any merge
          // could shift indices. The overrideKey uses these indices to apply toggles in doImport.
          if (valCorrections?.length) {
            farmer._valCorrections = valCorrections.map(c => {
              // The idx from validateExtraction points into the extracted data arrays.
              // buildFarmerFromExtraction filters/maps those arrays, so we re-find the row
              // by county+crop+type identity to get the correct index in the farmer's arrays.
              const arr = c.arrayName === 'approvedYields' ? farmer.approvedYields : farmer.ppEligibility;
              const realIdx = arr ? arr.findIndex(r =>
                r.county === c.county && (r.crop||'') === (c.crop||'') &&
                (r.type||'').toUpperCase() === (c.resolvedType||'').toUpperCase()
              ) : -1;
              return { ...c, idx: realIdx >= 0 ? realIdx : c.idx };
            }).filter(c => c.idx >= 0);
          } else {
            farmer._valCorrections = [];
          }
          let score = 0;
          if (farmer.name && farmer.name !== 'Unknown') score += 25;
          if (farmer.policyNumber) score += 25;
          if (farmer.cropYear) score += 15;
          // Score the data portion based on what the document type should contain
          const isDRP = data.documentType === 'SOI_DRP';
          const isLRP = data.documentType === 'LRP';
          const isPRF = data.documentType === 'PRF';
          const hasPolicyData = isDRP ? farmer.drpPolicies?.length
                              : isLRP ? farmer.lrpPolicies?.length
                              : isPRF ? farmer.prfPolicies?.length
                              : (farmer.approvedYields?.length || farmer.ppEligibility?.length);
          if (hasPolicyData) score += 35;
          // Deduct from score for validation issues
          score = Math.max(0, score - (valErrors.length * 10) - (valWarnings.length * 3));
          // Only show truncation warning if continuation passes didn't resolve it
          const endCount = farmer.drpPolicies?.[0]?.endorsements?.length || 0;
          const truncated = data._repaired && isDRP && endCount < 5;
          const noteParts = [];
          if (truncated) noteParts.push('⚠️ response may be truncated');
          if (valErrors.length)        noteParts.push(`🚨 ${valErrors.length} data error${valErrors.length>1?'s':''}`);
          if (valWarnings.length)      noteParts.push(`⚠️ ${valWarnings.length} warning${valWarnings.length>1?'s':''}`);
          if ((valCorrections||[]).length) noteParts.push(`🔍 ${valCorrections.length} type${valCorrections.length>1?'s':''} need confirmation`);;
          const note = noteParts.length ? ' · ' + noteParts.join(' · ') : '';
          setStatus(file.name, 'done', farmer, score, note);
          results.push({ file, farmer, docType: data.documentType, confidence: score, valWarnings, valErrors, valCorrections: valCorrections||[] });
          } // end else (non-BATCH_PP)
        } catch(err) {
          setStatus(file.name, 'error', err.message);
          results.push({ file, farmer: null, error: err.message });
        }
      }
      if (i < toProcess.length - 1) await new Promise(r => setTimeout(r, 3000));
    }

    extractionResults.current = results;
    const farmerMap = {};
    results.filter(r => r.farmer).forEach(r => {
      // DRP SOIs: key by name only — same farm may have 2025 + 2026 SOIs with different
      // policy numbers/cropYears that must merge into one farmer card.
      // All other docs: key by policyNumber+cropYear as before.
      const isDRP = r.docType === 'SOI_DRP';
      const key = isDRP
        ? ('drp_' + (r.farmer.name || 'unknown').trim().toLowerCase())
        : ((r.farmer.policyNumber || r.farmer.name || 'unknown') + '_' + r.farmer.cropYear);
      if (!farmerMap[key]) farmerMap[key] = { ...r, farmer: { ...r.farmer } };
      else { farmerMap[key].farmer = mergeFarmers(farmerMap[key].farmer, r.farmer); farmerMap[key].confidence = Math.max(farmerMap[key].confidence || 0, r.confidence || 0); }
    });

    const allMerged = Object.values(farmerMap).map(r => r.farmer);
    setMergedFarmers(allMerged);

    // PP_REPORT and pure-PP farmers (ppEligibility only, no approvedYields) should
    // ALWAYS be auto-merged into the existing farmer — never shown as a conflict.
    // PP data (ppEligibility) is complementary to crop data (approvedYields), never conflicting.
    // Showing "Keep Existing vs Replace" for a PP upload causes the PP data to be silently lost
    // when the user clicks "Keep Existing" (they're thinking about crop data, not PP).
    //
    // DRP SOI uploads should ALSO always auto-merge — a farmer may have two DRP SOIs
    // (one per crop year, e.g. 2025 + 2026) with the same policy number. The conflict UI
    // would let the user click "Keep Existing" and silently discard the second SOI's endorsements.
    // mergeFarmers handles DRP correctly: it unions endorsements from both SOIs.
    const isPPOnly    = f => f.ppEligibility?.length > 0 && !f.approvedYields?.length && !f.drpPolicies?.length && !f.lrpPolicies?.length;
    const isDRPOnly   = f => f.drpPolicies?.length > 0 && !f.approvedYields?.length && !f.ppEligibility?.length;
    const isLRPOnly   = f => f.lrpPolicies?.length > 0 && !f.approvedYields?.length && !f.ppEligibility?.length && !f.drpPolicies?.length;
    const isPRFOnly   = f => f.prfPolicies?.length > 0 && !f.approvedYields?.length && !f.ppEligibility?.length && !f.drpPolicies?.length && !f.lrpPolicies?.length;

    const isFallSOI   = f => f.approvedYields?.length > 0
      && f.approvedYields.every(y => FALL_CROPS.includes((y.crop||'').toUpperCase()))
      && !f.ppEligibility?.length && !f.drpPolicies?.length && !f.lrpPolicies?.length;
    // Wheat/fall SOIs are always standalone cards — never auto-merge, never conflict
    const isAutoMerge = f => isPPOnly(f) || isDRPOnly(f) || isLRPOnly(f) || isPRFOnly(f);
    const normName = s => (s||'').trim().toLowerCase();
    const findExisting = f => (isDRPOnly(f) || isLRPOnly(f) || isPRFOnly(f))
      ? farmers.find(ef => normName(ef.name) === normName(f.name))
      : farmers.find(ef => ef.policyNumber === f.policyNumber && ef.cropYear === f.cropYear);
    const autoMergeList = allMerged.filter(f => !isFallSOI(f) && findExisting(f) && isAutoMerge(f));
    const conflictList  = allMerged.filter(f => !isFallSOI(f) && findExisting(f) && !isAutoMerge(f));
    const cleanList     = allMerged.filter(f =>  isFallSOI(f) || !findExisting(f));

    // Auto-merge PP-only and DRP-only farmers into their existing counterpart immediately
    const autoMerged = autoMergeList.map(incoming => {
      const existing = findExisting(incoming);
      return existing ? mergeFarmers(existing, incoming) : incoming;
    });

    setConflicts(conflictList);
    setResolved([...cleanList, ...autoMerged]);
    setConflictIdx(0);
    if (conflictList.length > 0) setPhase('conflicts');
    else setPhase('review');
  }

  function keepExisting()    { if (conflictIdx + 1 < conflicts.length) setConflictIdx(i => i+1); else setPhase('review'); }
  function replaceExisting() {
    const incoming = conflicts[conflictIdx];
    const existing = farmers.find(f => f.policyNumber === incoming.policyNumber && f.cropYear === incoming.cropYear);
    const merged = existing ? mergeFarmers(existing, incoming) : incoming;
    setResolved(prev => [...prev, merged]);
    if (conflictIdx + 1 < conflicts.length) setConflictIdx(i => i+1); else setPhase('review');
  }

  async function doImport() {
    // Apply any manual GSG/GSS overrides before saving
    const finalResolved = resolved.map(farmer => {
      const overrideKeys = Object.keys(typeOverrides).filter(k => k.startsWith(`${farmer.policyNumber}|${farmer.cropYear}|`));
      if (!overrideKeys.length) return farmer;
      // Deep clone the farmer to avoid mutating resolved state
      const f = JSON.parse(JSON.stringify(farmer));
      overrideKeys.forEach(key => {
        const [, , arrayName, idxStr] = key.split('|');
        const idx = parseInt(idxStr, 10);
        const newType = typeOverrides[key];
        if (arrayName === 'approvedYields' && f.approvedYields?.[idx]) {
          f.approvedYields[idx].type = newType;
        } else if (arrayName === 'ppEligibility' && f.ppEligibility?.[idx]) {
          f.ppEligibility[idx].type = newType;
        }
      });
      return f;
    });

    setPhase('done');
    setSaveStatus('saving'); setSaveError(null);
    // The originals FIRST, so a save that fails below still leaves the PDF on file.
    // storeImportOriginals never throws; whatever it could not do is printed.
    const originals = await storeImportOriginals(extractionResults.current);
    const results = []; let saved = 0; const errors = [];
    for (const farmer of finalResolved) {
      try {
        const { steps, farmerId: savedFarmerId } = await saveFarmerToSupabase(farmer);
        saved++;
        results.push({ name: farmer.name, policyNumber: farmer.policyNumber, cropYear: farmer.cropYear, farmerId: savedFarmerId, ok: true, steps });
      } catch(err) {
        console.error("Save failed for", farmer.name, err);
        errors.push(`${farmer.name}: ${err.message}`);
        results.push({ name: farmer.name, policyNumber: farmer.policyNumber, ok: false, error: err.message, steps: [] });
      }
    }
    setSaveResults(results);
    setOriginalsReport(await attachImportOriginals(originals, results));
    // ⚠ ONLY WHAT SAVED reaches the app. The import used to put every farm on screen
    // BEFORE the save, so a farm whose save failed still showed as imported until the
    // next reload — the failure was visible only on this result screen.
    const savedFarms = DOCINDEX.farmersThatSaved(finalResolved, results);
    if (savedFarms.length) onImport(savedFarms, true);
    setSaveStatus(errors.length === 0 ? 'saved' : 'error');
    if (errors.length > 0) setSaveError(`${saved} saved · ${errors.length} FAILED — the failed farm${errors.length !== 1 ? 's were' : ' was'} not added to the book`);
  }

  function reset() {
    currentFiles.current = [];
    setFiles([]); setStatuses({}); setPhase('upload');
    setMergedFarmers([]); setConflicts([]); setConflictIdx(0); setResolved([]);
    setSaveStatus(null); setSaveError(null); setSaveResults([]); setOriginalsReport(null);
    setTypeOverrides({});
  }

  const STATUS_CFG = {
    waiting:     { icon:'⏳', label:'Queued',       color:'#888',    bg:'#f5f5f5' },
    reading:     { icon:'📄', label:'Reading...',   color:'#1a4a7a', bg:'#e8f0fa' },
    compressing: { icon:'🗜️', label:'Compressing...', color:'#6a3a7a', bg:'#f5e8fa' },
    extracting:  { icon:'🤖', label:'AI Reading...', color:'#7a5a00', bg:'#fff8e8' },
    done:        { icon:'✓',  label:'Extracted',    color:'#2d7a2d', bg:'#eef6ec' },
    error:       { icon:'✗',  label:'Error',        color:'#c0392b', bg:'#fdecea' },
    skipped:     { icon:'—',  label:'Skipped',      color:'#888',    bg:'#f5f5f5' },
    replaced:    { icon:'↺',  label:'Replaced',     color:'#2d4a2d', bg:'#e8f0e8' },
  };

  function FileRow({ file }) {
    const s = statuses[file.name] || { status:'waiting' };
    const cfg = STATUS_CFG[s.status] || STATUS_CFG.waiting;
    return (
      <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', borderBottom:'1px solid #f0f4f0' }}>
        <span style={{ fontSize:16, width:22, textAlign:'center', flexShrink:0 }}>{cfg.icon}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:13, color:'#1a2e1a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{file.name}</div>
          {s.result && s.status === 'done' && (
            <div style={{ fontSize:11, color:'#60735f', marginTop:2 }}>
              {s.result.name} · {s.result.policyNumber} · Year {s.result.cropYear}
              {s.result.ppEligibility?.length ? ` · ${s.result.ppEligibility.length} PP rows` : ''}
              {s.result.approvedYields?.length ? ` · ${s.result.approvedYields.length} yield lines` : ''}
            </div>
          )}
          {s.status === 'extracting' && s.result && typeof s.result === 'string' && (
            <div style={{ fontSize:11, color:'#c8860a', marginTop:2 }}>⏳ {s.result}</div>
          )}
          {s.status === 'error' && <div style={{ fontSize:11, color:'#c0392b', marginTop:2 }}>{s.result}</div>}
          {s.note && <div style={{ fontSize:11, color:'#c8860a', marginTop:2 }}>{s.note}</div>}
        </div>
        <span style={{ fontSize:11, fontWeight:700, color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.color}33`, padding:'2px 8px', borderRadius:4, flexShrink:0 }}>{cfg.label}</span>
        {s.confidence != null && s.status === 'done' && (
          <span style={{ fontSize:11, fontWeight:700, color: s.confidence >= 75 ? '#2d7a2d' : '#c8860a', minWidth:36, textAlign:'right' }}>{s.confidence}%</span>
        )}
        {phase === 'upload' && (
          <button onClick={() => removeFile(file.name)} style={{ background:'none', border:'none', color:'#ccc', cursor:'pointer', fontSize:16, padding:'0 2px', flexShrink:0 }}>×</button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ ...uiPageTitle(false), margin: '0 0 6px' }}>Import Policies</h2>
        <p style={{ margin:0, color:'#60735f', fontSize:13 }}>Drop multiple PDFs at once — AI reads each one and merges docs for the same farm automatically.</p>
      </div>
      {/* Seven tabs: they stay one line each and the row scrolls sideways when it
          does not fit, instead of squeezing labels onto two lines of uneven height. */}
      <div style={{ display:'flex', gap:4, borderBottom:'1px solid #e4ece4', marginBottom:20, overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
        {[
          {key:'pdf',      label:'📄  PDF Import (AI)'},
          {key:'excelimport', label:'📊  Excel Import'},
          {key:'historical',  label:'📜  Historical Data'},
          {key:'rma',         label:'📊  RMA Market Data'},
          {key:'audit',       label:'🔍  Audit / Compare'},
          {key:'lrpxlsx',     label:'🐄  LRP Bulk Import'},
          {key:'lrpindemnity', label:'📋  LRP Indemnity Import'},
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding:'9px 18px', border:'none', background:'none', borderBottom:activeTab===t.key?'3px solid #2d4a2d':'3px solid transparent', color:activeTab===t.key?'#1a2e1a':'#60735f', fontWeight:activeTab===t.key?800:500, fontSize:13, cursor:'pointer', marginBottom:-1, whiteSpace:'nowrap', flexShrink:0, fontFamily:'inherit' }}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'pdf' && (
        <div>
          {phase === 'upload' && (
            <div>
              <div onDragOver={handleDragOver} onDragEnter={handleDragOver} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop}
                style={{ border: isDragging ? '2px dashed #4a7a2a' : '2px dashed #c8d8c8', borderRadius:10, background: isDragging ? '#f0f7e8' : '#fafcfa', textAlign:'center', padding:'44px 24px', marginBottom:16, transition:'all 0.15s' }}>
                <div style={{ fontSize:44, marginBottom:10 }}>📄</div>
                <div style={{ fontSize:16, fontWeight:700, color:'#1a2e1a', marginBottom:6 }}>{isDragging ? 'Drop files here' : 'Drag & drop PDF or ZIP files'}</div>
                <div style={{ fontSize:13, color:'#60735f', marginBottom:6 }}>PP Reports, APH Database, SOI, DRP, LRP — all supported.</div>
                <div style={{ fontSize:12, color:'#4a7a4a', fontWeight:600, marginBottom:18 }}>📦 Drop a ZIP of 200+ PDFs for bulk agency import</div>
                <label style={{ display:'inline-block', padding:'10px 24px', background:'#2d4a2d', color:'#fff', borderRadius:7, fontWeight:800, fontSize:13, cursor:'pointer', letterSpacing:'0.06em' }}>
                  BROWSE FILES
                  <input type="file" accept=".pdf,application/pdf,.zip,application/zip" multiple onChange={handleFileInput} style={{ display:'none' }} />
                </label>
              </div>

              {/* ZIP batch progress UI */}
              {zipBatch && (
                <div style={{ background:'#fff', border:'1px solid #e4ece4', borderRadius:8, overflow:'hidden', marginBottom:16 }}>
                  {/* Header */}
                  <div style={{ padding:'12px 18px', background: zipBatch.phase==='done' ? '#f0f7f0' : '#1a2e1a', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                    <span style={{ fontSize:18 }}>
                      {zipBatch.phase==='extracting'?'📦':zipBatch.phase==='processing'?'🤖':zipBatch.phase==='merging'?'🔀':zipBatch.phase==='saving'?'💾':zipBatch.phase==='done'?'✅':'❌'}
                    </span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13, color: zipBatch.phase==='done'?'#1a2e1a':'#fff' }}>
                        {zipBatch.phase==='extracting' && 'Unpacking ZIP...'}
                        {zipBatch.phase==='processing' && `AI Reading PDFs — ${zipBatch.done||0} of ${zipBatch.total} complete`}
                        {zipBatch.phase==='merging'    && 'Merging farmer records...'}
                        {zipBatch.phase==='saving'     && `Saving to database...`}
                        {zipBatch.phase==='done'       && `Batch complete — ${zipBatch.saved} farmers saved`}
                        {zipBatch.phase==='error'      && 'Error'}
                      </div>
                      <div style={{ fontSize:11, color: zipBatch.phase==='done'?'#60735f':'#4a7a4a', marginTop:2 }}>
                        {zipBatch.zipName}
                        {zipBatch.phase==='processing' && ` · ${zipBatch.total} PDFs · 2 parallel workers`}
                        {zipBatch.phase==='done' && zipBatch.failed > 0 && ` · ${zipBatch.failed} failed`}
                      </div>
                    </div>
                    {/* Progress bar */}
                    {zipBatch.phase==='processing' && zipBatch.total > 0 && (
                      <div style={{ width:'100%', height:6, background:'#2d4a2d', borderRadius:3, marginTop:4 }}>
                        <div style={{ height:6, background:'#6aaa6a', borderRadius:3, transition:'width 0.3s',
                          width: ((zipBatch.done||0)/zipBatch.total*100)+'%' }} />
                      </div>
                    )}
                  </div>

                  {/* Live log */}
                  {(zipBatch.log||[]).length > 0 && (
                    <div style={{ maxHeight:200, overflowY:'auto', padding:'8px 0', borderBottom:'1px solid #f0f4f0' }}>
                      {[...(zipBatch.log||[])].reverse().map((entry, i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'3px 14px', fontSize:12 }}>
                          <span style={{ color: entry.ok ? '#2d7a2d':'#c0392b', flexShrink:0 }}>{entry.ok?'✓':'✗'}</span>
                          <span style={{ color:'#555', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{entry.name}</span>
                          {entry.docType && <span style={{ fontSize:10, color:'#60735f', flexShrink:0 }}>{entry.docType}</span>}
                          {entry.error  && <span style={{ fontSize:10, color:'#c0392b', flexShrink:0 }}>{entry.error.slice(0,40)}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Done summary */}
                  {zipBatch.phase==='done' && (
                    <div style={{ padding:'12px 18px' }}>
                      <div style={{ display:'flex', gap:20, flexWrap:'wrap', marginBottom:10 }}>
                        {[
                          { lbl:'PDFs Processed', val: zipBatch.total },
                          { lbl:'Farmers Saved',  val: zipBatch.saved,  color:'#2d7a2d' },
                          { lbl:'Extraction Failures', val: zipBatch.failed, color: zipBatch.failed>0?'#c0392b':'#2d7a2d' },
                          { lbl:'Existing Records Updated', val: (zipBatch.overwriteLog||[]).length, color:'#7a5a00' },
                        ].map(s => (
                          <div key={s.lbl}>
                            <div style={{ fontSize:10, fontWeight:800, letterSpacing:'0.1em', color:'#60735f', textTransform:'uppercase' }}>{s.lbl}</div>
                            <div style={{ fontSize:20, fontWeight:800, color: s.color||'#1a2e1a' }}>{s.val}</div>
                          </div>
                        ))}
                      </div>
                      {(zipBatch.overwriteLog||[]).length > 0 && (
                        <div style={{ background:'#fffbe8', border:'1px solid #e8d870', borderRadius:6, padding:'8px 12px', fontSize:11, color:'#7a6a00' }}>
                          <strong>Updated existing records:</strong> {zipBatch.overwriteLog.map(r=>r.name).join(', ')}
                        </div>
                      )}
                      {(zipBatch.saveErrors||[]).length > 0 && (
                        <div style={{ marginTop:8, background:'#fdecea', border:'1px solid #f5c6cb', borderRadius:6, padding:'8px 12px', fontSize:11, color:'#c0392b' }}>
                          <strong>Save errors:</strong> {zipBatch.saveErrors.map(e=>e.name).join(', ')} — not added to the book
                        </div>
                      )}
                      <div style={{ marginTop:8 }}><ImportOriginalsNote stored={originalsReport} /></div>
                      <div style={{ marginTop:10, display:'flex', gap:10, flexWrap:'wrap' }}>
                        {(zipBatch.failedFiles||[]).length > 0 && (
                          <button
                            onClick={() => {
                              const retryFiles = zipBatch.failedFiles;
                              setZipBatch(null);
                              // Re-queue failed files as regular PDFs
                              setTimeout(() => {
                                currentFiles.current = retryFiles;
                                setFiles([...retryFiles]);
                                retryFiles.forEach(f => setStatus(f.name, 'waiting'));
                                setPhase('upload');
                              }, 100);
                            }}
                            style={{ padding:'7px 18px', background:'#c0392b', color:'#fff', border:'none', borderRadius:6, fontWeight:700, fontSize:12, cursor:'pointer' }}>
                            ↺ Retry {zipBatch.failedFiles.length} Failed PDFs
                          </button>
                        )}
                        <button onClick={() => setZipBatch(null)} style={{ padding:'7px 18px', background:'#2d4a2d', color:'#fff', border:'none', borderRadius:6, fontWeight:700, fontSize:12, cursor:'pointer' }}>
                          Import Another File
                        </button>
                      </div>
                    </div>
                  )}

                  {zipBatch.phase==='error' && (
                    <div style={{ padding:'12px 18px', color:'#c0392b', fontSize:13 }}>
                      {zipBatch.errorMsg}
                      <button onClick={() => setZipBatch(null)} style={{ marginLeft:16, padding:'5px 14px', background:'none', border:'1px solid #c0392b', color:'#c0392b', borderRadius:5, cursor:'pointer', fontSize:12 }}>Dismiss</button>
                    </div>
                  )}
                </div>
              )}
              {files.length > 0 && (
                <Card style={{ padding:0, overflow:'hidden', marginBottom:16 }}>
                  <div style={{ padding:'12px 16px', background:'#f4f8f4', borderBottom:'1px solid #e4ece4', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontWeight:700, fontSize:13, color:'#1a2e1a' }}>{files.length} PDF{files.length !== 1 ? 's' : ''} queued</span>
                    <button onClick={reset} style={{ fontSize:11, color:'#c0392b', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Clear all</button>
                  </div>
                  {files.map(f => <FileRow key={f.name} file={f} />)}
                  <div style={{ padding:'14px 16px', borderTop:'1px solid #e4ece4', background:'#f9fbf9', display:'flex', alignItems:'center', gap:14 }}>
                    <button onClick={processAll} style={{ padding:'11px 28px', background:'#2d4a2d', color:'#fff', border:'none', borderRadius:7, fontWeight:800, fontSize:13, letterSpacing:'0.08em', cursor:'pointer' }}>🤖 EXTRACT WITH AI</button>
                    <span style={{ fontSize:11, color:'#60735f' }}>~10s per PDF</span>
                  </div>
                </Card>
              )}
            </div>
          )}

          {phase === 'processing' && (
            <Card style={{ padding:0, overflow:'hidden' }}>
              <div style={{ padding:'14px 16px', background:'#1a2e1a', color:'#7aaa7a', fontWeight:700, fontSize:13, display:'flex', alignItems:'center', gap:10 }}>
                <span>🤖</span>
                <span>AI is reading {files.length} document{files.length !== 1 ? 's' : ''}...</span>
                <span style={{ marginLeft:'auto', fontSize:11, color:'#4a7a4a' }}>{Object.values(statuses).filter(s => s.status === 'done' || s.status === 'error').length} / {files.length} complete</span>
              </div>
              {files.map(f => <FileRow key={f.name} file={f} />)}
            </Card>
          )}

          {phase === 'conflicts' && conflicts.length > 0 && (
            <div>
              <Card style={{ marginBottom:16, background:'#fffbf0', border:'1px solid #f0c840' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                  <span style={{ fontSize:22 }}>⚠️</span>
                  <div style={{ fontWeight:800, fontSize:15, color:'#1a2e1a' }}>Conflict {conflictIdx + 1} of {conflicts.length}</div>
                  <span style={{ fontSize:11, color:'#60735f', marginLeft:'auto' }}>{resolved.length} ready to import</span>
                </div>
              </Card>
              <div style={{ border:'1px solid #f0c840', borderRadius:8, padding:'18px', background:'#fffbf0', marginBottom:16 }}>
                <div style={{ fontWeight:800, fontSize:15, color:'#1a2e1a', marginBottom:4 }}>{conflicts[conflictIdx].name}</div>
                <div style={{ fontSize:12, color:'#60735f', marginBottom:14 }}>
                  {conflicts[conflictIdx].policyNumber} · Crop Year {conflicts[conflictIdx].cropYear}<br/>
                  This farm already has data on file. The uploaded PDF has:
                  <strong> {conflicts[conflictIdx].ppEligibility?.length || 0} PP rows</strong>,
                  <strong> {conflicts[conflictIdx].approvedYields?.length || 0} yield lines</strong>,
                  <strong> {conflicts[conflictIdx].drpPolicies?.flatMap(p=>p.endorsements||[]).length || 0} DRP endorsements</strong>,
                  <strong> {conflicts[conflictIdx].lrpPolicies?.flatMap(p=>p.endorsements||[]).length || 0} LRP endorsements</strong>.
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <button onClick={keepExisting} style={{ flex:1, padding:'10px', border:'1px solid #dde5dd', borderRadius:7, background:'#fff', color:'#4a6e4a', fontWeight:700, fontSize:13, cursor:'pointer' }}>Keep Existing Data</button>
                  <button onClick={replaceExisting} style={{ flex:1, padding:'10px', border:'none', borderRadius:7, background:'#c0392b', color:'#fff', fontWeight:800, fontSize:13, cursor:'pointer' }}>Update with New PDF Data</button>
                </div>
              </div>
            </div>
          )}

          {phase === 'review' && (
            <div>
              {/* ── EXTRACTION FAILURES — shown prominently if any files errored ── */}
              {(() => {
                const failed = files.filter(f => statuses[f.name]?.status === 'error');
                if (!failed.length) return null;
                return (
                  <div style={{ marginBottom:16, background:'#fdecea', border:'2px solid #c0392b44', borderRadius:10, overflow:'hidden' }}>
                    <div style={{ padding:'12px 16px', background:'#c0392b', display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:18 }}>⚠️</span>
                      <div>
                        <div style={{ fontWeight:800, fontSize:14, color:'#fff' }}>{failed.length} file{failed.length !== 1 ? 's' : ''} failed to extract — not included in import</div>
                        <div style={{ fontSize:12, color:'#ffcccc', marginTop:2 }}>These will need to be re-uploaded. Common causes: file too large, PDF unreadable, or AI timeout.</div>
                      </div>
                    </div>
                    <div style={{ padding:'8px 0' }}>
                      {failed.map(f => (
                        <div key={f.name} style={{ padding:'9px 16px', borderBottom:'1px solid #f8d7d7', display:'flex', alignItems:'flex-start', gap:10 }}>
                          <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>✗</span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:700, fontSize:13, color:'#a02020', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</div>
                            <div style={{ fontSize:11, color:'#c0392b', marginTop:2 }}>{statuses[f.name]?.result || 'Unknown error'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <Card style={{ marginBottom:16 }}>
                <SectionTitle>READY TO IMPORT — {resolved.length} FARM{resolved.length !== 1 ? 'S' : ''}</SectionTitle>
                {resolved.length === 0 ? (
                  <div style={{ color:'#60735f', fontSize:13, padding:'8px 0' }}>No new data to import.</div>
                ) : resolved.map((f, i) => {
                  // valCorrections are attached directly to the farmer object — survive merges
                  const resultEntry = extractionResults.current?.find?.(r => r.farmer?.policyNumber === f.policyNumber && r.farmer?.cropYear === f.cropYear);
                  const valErrors      = resultEntry?.valErrors      || [];
                  const valWarnings    = resultEntry?.valWarnings    || [];
                  const valCorrections = f._valCorrections || [];

                  // For each correction, check if it's been confirmed via typeOverrides
                  const overrideKey = (c) => `${f.policyNumber}|${f.cropYear}|${c.arrayName}|${c.idx}`;
                  const pendingCorrections = valCorrections.filter(c => typeOverrides[overrideKey(c)] == null);
                  const confirmedCorrections = valCorrections.filter(c => typeOverrides[overrideKey(c)] != null);

                  const hasIssues = valErrors.length > 0 || valWarnings.length > 0;
                  const hasPending = pendingCorrections.length > 0;
                  return (
                  <div key={i} style={{ padding:'12px 0', borderBottom:'1px solid #f0f4f0' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:800, fontSize:14, color:'#1a2e1a' }}>{f.name}</div>
                        <div style={{ fontSize:12, color:'#60735f', marginTop:2 }}>{f.policyNumber} · Year {f.cropYear} · {f.agent}</div>
                        <div style={{ display:'flex', gap:6, marginTop:6, flexWrap:'wrap' }}>
                          {f.policyTypes?.map(t => <TypeBadge key={t} type={t} />)}
                          {f.ppEligibility?.length > 0 && <span style={{ fontSize:11, color:'#c8860a', background:'#fef9ec', border:'1px solid #c8860a33', padding:'2px 7px', borderRadius:4 }}>{f.ppEligibility.length} PP rows</span>}
                          {f.approvedYields?.length > 0 && <span style={{ fontSize:11, color:'#3a6e32', background:'#eef6ec', border:'1px solid #3a6e3233', padding:'2px 7px', borderRadius:4 }}>{f.approvedYields.length} yield lines</span>}
                          {(f.drpPolicies?.flatMap(p=>p.endorsements||[]).length||0) > 0 && <span style={{ fontSize:11, color:'#1a4a7a', background:'#e8f0fa', border:'1px solid #1a4a7a33', padding:'2px 7px', borderRadius:4 }}>{f.drpPolicies.flatMap(p=>p.endorsements||[]).length} DRP end.</span>}
                          {(f.lrpPolicies?.flatMap(p=>p.endorsements||[]).length||0) > 0 && <span style={{ fontSize:11, color:'#7a3b10', background:'#fdf1e8', border:'1px solid #c8603033', padding:'2px 7px', borderRadius:4 }}>{f.lrpPolicies.flatMap(p=>p.endorsements||[]).length} LRP end.</span>}
                        </div>

                        {/* ── TYPE CONFIRMATION REQUIRED ── */}
                        {(hasPending || confirmedCorrections.length > 0) && (
                          <div style={{ marginTop:10 }}>
                            {hasPending && (
                              <div style={{ marginBottom:6, padding:'5px 10px', background:'#fff8e8', border:'1px solid #e8c840', borderRadius:5, fontSize:11, color:'#7a5a00', fontWeight:700, display:'flex', alignItems:'center', gap:6 }}>
                                <span>🔍</span>
                                <span>{pendingCorrections.length} corn type{pendingCorrections.length>1?'s':''} need confirmation before import</span>
                              </div>
                            )}
                            {valCorrections.map((c) => {
                              const key = overrideKey(c);
                              const confirmed = typeOverrides[key];
                              const isAPH = c.arrayName === 'approvedYields';
                              const label = isAPH
                                ? `${c.county} CORN APH${c.approvedYield != null ? ` (${c.approvedYield}${c.resolvedType==='GSS'?' ton':' bu'})` : ''}`
                                : `${c.county} CORN PP`;
                              return (
                                <div key={key} style={{
                                  marginBottom:6,
                                  padding:'8px 10px',
                                  background: confirmed ? '#eef6ec' : '#fffbf0',
                                  border: `1px solid ${confirmed ? '#4a7a2a44' : '#e8c84077'}`,
                                  borderRadius:6,
                                  display:'flex', alignItems:'flex-start', gap:8, flexWrap:'wrap'
                                }}>
                                  <div style={{ flex:1, minWidth:180 }}>
                                    <div style={{ fontSize:11, fontWeight:700, color:'#1a2e1a', marginBottom:2 }}>{label}</div>
                                    <div style={{ fontSize:10, color:'#7a6a00' }}>{c.reason}</div>
                                    {c.rawType && (
                                      <div style={{ fontSize:10, color:'#9a8a60', marginTop:1 }}>
                                        PDF read: <code style={{ background:'#f5f0e0', padding:'1px 4px', borderRadius:3 }}>{c.rawType}</code>
                                        {' '}→ stored as <code style={{ background:'#f5f0e0', padding:'1px 4px', borderRadius:3 }}>{c.resolvedType}</code>
                                      </div>
                                    )}
                                  </div>
                                  {/* GSG / GSS toggle */}
                                  <div style={{ display:'flex', gap:4, flexShrink:0, alignItems:'center' }}>
                                    <span style={{ fontSize:10, color:'#60735f', marginRight:2 }}>Correct type:</span>
                                    {['GSG','GSS'].map(opt => (
                                      <button
                                        key={opt}
                                        onClick={() => setTypeOverrides(prev => ({ ...prev, [key]: opt }))}
                                        style={{
                                          padding:'4px 12px',
                                          fontSize:11, fontWeight:800,
                                          border: confirmed===opt ? 'none' : '1px solid #ccc',
                                          borderRadius:4,
                                          cursor:'pointer',
                                          background: confirmed===opt
                                            ? (opt==='GSG' ? '#2d7a2d' : '#5a3a8a')
                                            : '#fff',
                                          color: confirmed===opt ? '#fff' : '#444',
                                          transition:'all 0.1s',
                                        }}
                                      >{opt}</button>
                                    ))}
                                    {confirmed && (
                                      <span style={{ fontSize:10, color:'#2d7a2d', fontWeight:700, marginLeft:4 }}>✓ Confirmed</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {hasIssues && (
                          <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:3 }}>
                            {valErrors.map((e, ei) => (
                              <div key={ei} style={{ fontSize:11, color:'#a02020', background:'#fdecea', border:'1px solid #c0392b33', borderRadius:4, padding:'3px 8px', display:'flex', gap:5, alignItems:'flex-start' }}>
                                <span style={{ flexShrink:0 }}>🚨</span><span><strong>Data error:</strong> {e.msg}</span>
                              </div>
                            ))}
                            {valWarnings.map((w, wi) => (
                              <div key={wi} style={{ fontSize:11, color:'#7a5a00', background:'#fffbf0', border:'1px solid #c8860a33', borderRadius:4, padding:'3px 8px', display:'flex', gap:5, alignItems:'flex-start' }}>
                                <span style={{ flexShrink:0 }}>⚠️</span><span>{w.msg}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize:11, color: valErrors.length ? '#c0392b' : hasPending ? '#8a6000' : valWarnings.length ? '#8a6000' : '#2d7a2d', fontWeight:800, flexShrink:0, marginLeft:12 }}>
                        {valErrors.length ? '🚨 Errors' : hasPending ? '🔍 Confirm Types' : valWarnings.length ? '⚠️ Warnings' : '✓ Ready'}
                      </span>
                    </div>
                  </div>
                  );
                })}
              </Card>
              <Card style={{ padding:0, overflow:'hidden', marginBottom:16 }}>
                <div style={{ padding:'10px 16px', background:'#f4f8f4', borderBottom:'1px solid #e4ece4', fontWeight:700, fontSize:12, color:'#1a2e1a' }}>
                  All {files.length} Files
                  <span style={{ fontWeight:400, color:'#60735f', marginLeft:10 }}>
                    {Object.values(statuses).filter(s=>s.status==='done').length} extracted · {Object.values(statuses).filter(s=>s.status==='error').length} errors
                  </span>
                </div>
                {files.map(f => <FileRow key={f.name} file={f} />)}
              </Card>
              <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                {resolved.length > 0 && (() => {
                  // Count all pending (unconfirmed) corrections across all resolved farmers
                  // Read _valCorrections directly from farmer objects — they survive merges correctly
                  const allPending = resolved.reduce((sum, f) => {
                    if (!f._valCorrections?.length) return sum;
                    return sum + f._valCorrections.filter(c => {
                      const key = `${f.policyNumber}|${f.cropYear}|${c.arrayName}|${c.idx}`;
                      return typeOverrides[key] == null;
                    }).length;
                  }, 0);
                  const blocked = allPending > 0;
                  return (
                    <>
                      <button
                        onClick={blocked ? undefined : doImport}
                        disabled={blocked}
                        style={{
                          padding:'12px 28px',
                          background: blocked ? '#677167' : '#2d4a2d',
                          color:'#fff', border:'none', borderRadius:7, fontWeight:800,
                          fontSize:13, letterSpacing:'0.08em',
                          cursor: blocked ? 'not-allowed' : 'pointer',
                          opacity: blocked ? 0.75 : 1,
                        }}>
                        IMPORT {resolved.length} FARM{resolved.length !== 1 ? 'S' : ''} →
                      </button>
                      {blocked && (
                        <span style={{ fontSize:12, color:'#8a6000', fontWeight:600 }}>
                          ⚠ Confirm {allPending} corn type{allPending>1?'s':''} above before importing
                        </span>
                      )}
                    </>
                  );
                })()}
                <button onClick={reset} style={{ padding:'12px 20px', background:'none', border:'1px solid #dde5dd', color:'#4a6e4a', borderRadius:7, fontWeight:700, fontSize:13, cursor:'pointer' }}>Start Over</button>
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div>
              {saveStatus === 'saving' && (
                <Card style={{ textAlign:'center', padding:'48px 24px' }}>
                  <div style={{ fontSize:48, marginBottom:16 }}>💾</div>
                  <div style={{ fontSize:18, fontWeight:800, color:'#1a2e1a', marginBottom:8 }}>Saving to Database…</div>
                  <div style={{ fontSize:13, color:'#60735f' }}>Writing {resolved.length} farm{resolved.length !== 1 ? 's' : ''} to Supabase…</div>
                </Card>
              )}
              {(saveStatus === 'saved' || saveStatus === 'error') && (
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, padding:'14px 20px', borderRadius:10, background: saveStatus==='saved'?'#eef6ec':'#fdecea', border:`1px solid ${saveStatus==='saved'?'#4a7a2a44':'#c0392b44'}` }}>
                    <span style={{ fontSize:24 }}>{saveStatus==='saved'?'✅':'⚠️'}</span>
                    <div>
                      <div style={{ fontWeight:800, fontSize:15, color:'#1a2e1a' }}>{saveStatus==='saved' ? 'All farms saved successfully' : saveError}</div>
                      <div style={{ fontSize:12, color:'#60735f', marginTop:2 }}>Each row below shows exactly what was written to Supabase</div>
                    </div>
                    <button onClick={reset} style={{ marginLeft:'auto', padding:'9px 20px', background:'#2d4a2d', color:'#fff', border:'none', borderRadius:7, fontWeight:800, fontSize:12, cursor:'pointer', flexShrink:0 }}>Upload More</button>
                  </div>
                  <ImportOriginalsNote stored={originalsReport} />
                  {/* Extraction failures — files that never made it to save */}
                  {(() => {
                    const failed = files.filter(f => statuses[f.name]?.status === 'error');
                    if (!failed.length) return null;
                    return (
                      <div style={{ marginBottom:16, background:'#fdecea', border:'2px solid #c0392b44', borderRadius:10, overflow:'hidden' }}>
                        <div style={{ padding:'10px 16px', background:'#c0392b', display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontSize:16 }}>⚠️</span>
                          <div style={{ fontWeight:800, fontSize:13, color:'#fff' }}>{failed.length} file{failed.length !== 1 ? 's' : ''} failed extraction — re-upload these separately</div>
                        </div>
                        {failed.map(f => (
                          <div key={f.name} style={{ padding:'9px 16px', borderBottom:'1px solid #f8d7d7', display:'flex', alignItems:'flex-start', gap:10 }}>
                            <span style={{ fontSize:13, flexShrink:0, marginTop:1, color:'#c0392b' }}>✗</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:700, fontSize:13, color:'#a02020', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</div>
                              <div style={{ fontSize:11, color:'#c0392b', marginTop:2 }}>{statuses[f.name]?.result || 'Unknown error'}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <Card style={{ padding:0, overflow:'hidden' }}>
                    {saveResults.map((r, i) => (
                      <div key={i} style={{ borderBottom:'1px solid #f0f4f0' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', background: r.ok?'#f4f8f4':'#fdecea' }}>
                          <span style={{ fontSize:14 }}>{r.ok ? '✅' : '❌'}</span>
                          <div style={{ flex:1 }}>
                            <span style={{ fontWeight:700, fontSize:13, color:'#1a2e1a' }}>{r.name}</span>
                            {r.policyNumber && <span style={{ fontSize:11, color:'#60735f', marginLeft:8 }}>#{r.policyNumber}</span>}
                          </div>
                          {r.ok ? <span style={{ fontSize:11, color:'#2d7a2d', fontWeight:700 }}>SAVED</span> : <span style={{ fontSize:11, color:'#c0392b', fontWeight:700 }}>FAILED</span>}
                        </div>
                        {r.ok && r.steps?.length > 0 && (
                          <div style={{ padding:'8px 16px 10px 44px', display:'flex', flexWrap:'wrap', gap:'4px 12px' }}>
                            {r.steps.map((s, j) => (
                              <span key={j} style={{ fontSize:10, color: s.ok?'#2d7a2d':'#c0392b', fontVariantNumeric: "tabular-nums" }}>
                                {s.ok ? '✓' : '✗'} {s.step}: {s.detail}
                              </span>
                            ))}
                          </div>
                        )}
                        {!r.ok && r.error && (
                          <div style={{ padding:'8px 16px 10px 44px', fontSize:11, color:'#c0392b', fontVariantNumeric: "tabular-nums" }}>{r.error}</div>
                        )}
                      </div>
                    ))}
                  </Card>
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {activeTab === 'excelimport' && (() => {
        // ── POLICY DATA EXCEL IMPORT (NAU + Rain & Hail) ──
        // Auto-detects AIP from column headers:
        //   NAU: "Agency Code" + "Aph Yield" columns (NAU Prevented Plant Liability report)
        //   R&H: "Yield Line ID" + "Approved Yield" columns (R&H Data Set Builder export)
        // Matches farmers by policy number → policy_id
        // Full replace of non-wheat crop_coverage and pp_eligibility rows per policy
        // Re-uploadable: wipe + re-insert prevents duplicates

        const FALL_CROPS_SKIP = ['WHEAT'];
        const FALL_CROPS_KEEP = ['BARLEY','TRITICALE','RYE'];

        // ── FIPS code → county name maps (state-scoped) ──
        const FIPS_MAP = {
          '36': {3:'ALLEGANY',9:'CATTARAUGUS',11:'CAYUGA',13:'CHAUTAUQUA',15:'CHEMUNG',17:'CHENANGO',
                 19:'CLINTON',21:'CORTLAND',25:'DELAWARE',27:'DUTCHESS',29:'ERIE',37:'GENESEE',
                 49:'LIVINGSTON',51:'LIVINGSTON',55:'MONROE',63:'NIAGARA',65:'ONEIDA',67:'ONONDAGA',
                 69:'ONTARIO',73:'ORLEANS',75:'OSWEGO',77:'OTSEGO',99:'SENECA',101:'STEUBEN',
                 107:'TIOGA',109:'TOMPKINS',117:'WAYNE',121:'WYOMING',123:'YATES'},
          '42': {9:'BEDFORD',13:'BLAIR',15:'BRADFORD',21:'CAMBRIA',27:'CENTRE',49:'ERIE',
                 57:'FULTON',61:'HUNTINGDON',67:'JUNIATA',71:'LANCASTER',85:'MERCER',87:'MIFFLIN',
                 117:'TIOGA',133:'YORK'},
          '39': {3:'ALLEN',11:'AUGLAIZE',33:'CRAWFORD',39:'DEFIANCE',51:'FULTON',69:'HENRY',
                 77:'HURON',95:'LUCAS',123:'OTTAWA',137:'PUTNAM',143:'SANDUSKY',147:'SENECA',
                 155:'TRUMBULL',161:'VAN WERT',171:'WILLIAMS',175:'WYANDOT'},
          '18': {3:'ALLEN',33:'DEKALB',113:'NOBLE',151:'STEUBEN',179:'WELLS',183:'WHITLEY'},
          '24': {1:'ALLEGANY',5:'BALTIMORE',9:'CALVERT',13:'CARROLL',15:'CECIL',17:'CHARLES',
                 21:'FREDERICK',23:'GARRETT',25:'HARFORD',27:'HOWARD',29:'KENT'},
        };

        const fipsToCounty = (stateCode, countyFips) => {
          const sc = String(stateCode);
          const cf = parseInt(countyFips);
          return FIPS_MAP[sc]?.[cf] || `COUNTY-${cf}`;
        };

        // ── NAU plan code → plan name ──
        const NAU_PLAN_MAP = {1:'YP', 2:'RP', 90:'APH'};

        // ── R&H type mapping ──
        const RH_TYPE_MAP = {
          'GRAIN':'GSG','SILAGE':'GSS','NO TYPE SPECIFIED':'NTS','COMMODITY':'NTS',
          'DARK RED KIDNEY':'DRK','LIGHT RED KIDNEY':'LRK','BLACK':'BLK','CRANBERRY':'CBRY',
          'ALL TYPES':'ALL','WINTER':'W','ALL OTHERS (WINTER)':'W',
          'GREEN AND OTHER UNLISTED TYPES':'GRNOTH','MALTING (SPRING)':'S',
        };

        // ── R&H practice mapping ──
        const RH_PRACTICE_MAP = (p) => {
          if (!p) return null;
          const u = p.toUpperCase();
          if (u.includes('ORGANIC')) return 'Organic';
          if (u.includes('NFAC')) return 'Non-Irrigated';
          if (u === 'NON-IRRIGATED') return 'Non-Irrigated';
          if (u === 'IRRIGATED') return 'Irrigated';
          if (u === 'NO PRACTICE SPECIFIED') return 'Non-Irrigated';
          return null; // Index intervals, endorsement months → skip
        };

        // ── R&H plan mapping ──
        const RH_PLAN_MAP = (p) => {
          if (!p) return null;
          const u = p.toUpperCase();
          if (u === 'REVENUE PROTECTION') return 'RP';
          if (u === 'YIELD PROTECTION') return 'YP';
          if (u === 'APH') return 'APH';
          if (u.includes('SUPP COV OPT - REV')) return 'SCO-RP';
          if (u.includes('SUPP COV OPT - YIELD')) return 'SCO-YP';
          if (u.includes('ENHANCED COV OPT - REV')) return 'ECO-RP';
          if (u.includes('ENHANCED COV OPT - YIELD')) return 'ECO-YP';
          return null; // DRP, LRP, PRF → skip
        };

        // ── NAU crop name normalization ──
        const normCrop = (c) => {
          const u = (c||'').toUpperCase().trim();
          if (u === 'GRAIN SORG') return 'GRAIN SORGHUM';
          if (u === 'PROC BEANS') return 'PROCESSING BEANS';
          if (u === 'TRICL') return 'TRITICALE';
          return u;
        };

        // ── Load XLSX via SheetJS ──
        const loadXlsx = async (file) => {
          if (!window.XLSX) {
            await new Promise((res,rej) => {
              const s = document.createElement('script');
              s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
              s.onload = res; s.onerror = rej;
              document.head.appendChild(s);
            });
          }
          const buf = await file.arrayBuffer();
          const wb = window.XLSX.read(buf, {cellDates:false, raw:false});
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = window.XLSX.utils.sheet_to_json(ws, {defval:'', raw:false});
          const headers = rows.length ? Object.keys(rows[0]) : [];
          const safeRows = rows.map(r => {
            const out = {};
            for (const k of Object.keys(r)) {
              const v = r[k];
              out[k] = (v === null || v === undefined) ? '' : (typeof v === 'object') ? String(v) : v;
            }
            return out;
          });
          return { rows: safeRows, headers };
        };

        // ── Detect format ──
        const detectFormat = (headers) => {
          const h = headers.map(x => String(x).toLowerCase().trim());
          if (h.includes('agency code') && h.includes('aph yield')) return 'NAU_PPL';
          if (h.includes('total production') && h.includes('net production') && h.includes('net acres')) return 'NAU_PROD';
          if (h.includes('yield line id') && h.includes('approved yield') && h.includes('practice name')) return 'RH_DSB';
          if (h.includes('approved yield') && h.includes('sort name') && !h.includes('yield line id')) return 'RH_SIMPLE';
          if (h.includes('livestock policy') && h.includes('lrp coverage price')) return 'RH_LRP';
          // R&H PP report has a title row — check for "MPCI Prevented Planting"
          if (h.some(x => x.includes('mpci prevented planting'))) return 'RH_PP_TITLE';
          if (h.includes('max eligible acres') && h.includes('remaining eligible acres')) return 'RH_PP';
          // R&H production report has a title row — check if first column contains "Total Production Report"
          // and second-row headers include Commodity + Net Production
          if (h.some(x => x.includes('total production report'))) return 'RH_PROD_TITLE';
          if (h.includes('commodity') && h.includes('net production*')) return 'RH_PROD';
          return 'Unknown';
        };

        // ── Parse NAU Prevented Plant Liability report ──
        const parseNAU = (rows) => {
          const results = [];
          rows.forEach(r => {
            const crop = normCrop(r['Crop']);
            if (!crop) return;
            const planCode = parseInt(r['Plan']);
            const plan = NAU_PLAN_MAP[planCode];
            if (!plan) return;
            const stateCode = String(r['State Code']||'').trim();
            const countyFips = parseInt(r['County']);
            const county = fipsToCounty(stateCode, countyFips);
            const policyNum = String(r['Display Policy Number']||'').trim();
            const insName = String(r['Ins Name']||'').trim();
            const unitNum = String(r['Unit Number']||'').trim();
            const type = String(r['Type']||'').trim();
            const practice = String(r['Practice']||'').trim();
            const pracNorm = practice.includes('IRROC') ? 'Organic'
                           : practice.includes('IRRIGATED') ? 'Irrigated'
                           : practice.includes('NON IRR') || practice.includes('NPS') || practice.includes('NFAC') ? 'Non-Irrigated'
                           : practice.includes('SUMR') ? 'Summer Planted' : practice;
            const levelStr = String(r['Level']||'0').replace('%','');
            const level = parseFloat(levelStr) / 100;
            const ppLevelStr = String(r['PP Level']||'0').replace('%','');
            const ppLevel = parseFloat(ppLevelStr) / 100;
            const rawYield = String(r['Aph Yield']||'').replace(/,/g,'');
            const aphYield = (rawYield === '' || rawYield === 'NaN' || rawYield === undefined || isNaN(parseFloat(rawYield)))
              ? null : parseFloat(rawYield);
            const price = parseFloat(String(r['Price']||'0').replace(/[$,]/g,'')) || null;
            const ppLiab = parseFloat(String(r['Per Acre PP Liability']||'0').replace(/[$,]/g,'')) || null;
            const share = parseFloat(String(r['Share']||'1').replace(/,/g,'')) || 1;

            results.push({
              aip: 'NAU', policyNumber: policyNum, insuredName: insName,
              county: county.toUpperCase(), crop, plan, type, practice: pracNorm,
              unitNumber: unitNum, coverageLevel: level, aphYield, price,
              ppLevel, ppLiabilityPerAcre: ppLiab, share,
              isWheat: FALL_CROPS_SKIP.includes(crop), isFallCrop: FALL_CROPS_KEEP.includes(crop),
            });
          });
          return results;
        };

        // ── Parse NAU Prior Year Net Production report ──
        const parseNAU_PROD = (rows) => {
          const results = [];
          rows.forEach(r => {
            const crop = normCrop(r['Crop']);
            if (!crop) return;
            const county = String(r['County']||'').trim().toUpperCase();
            const policyNum = String(r['Policy Number']||'').trim();
            const insName = String(r['Insured']||'').trim();
            const measure = String(r['Measure']||'BU').trim().toUpperCase();
            const totalProd = parseFloat(String(r['Total Production']||'0').replace(/,/g,'')) || 0;
            const totalAcres = parseFloat(String(r['Total Acres']||'0').replace(/,/g,'')) || 0;
            const netProd = parseFloat(String(r['Net Production']||'0').replace(/,/g,'')) || 0;
            const netAcres = parseFloat(String(r['Net Acres']||'0').replace(/,/g,'')) || 0;
            const state = String(r['State']||'').trim().toUpperCase();
            // Derive state abbreviation from full name
            const STATE_ABBR = {'NEW YORK':'NY','PENNSYLVANIA':'PA','OHIO':'OH','INDIANA':'IN','MARYLAND':'MD'};
            const stateAbbr = STATE_ABBR[state] || state;
            // Use net production/acres (excludes prevented plant)
            if (netAcres <= 0) return;
            // Derive crop year from policy number (last 2 digits = policy year)
            // This is a PRIOR YEAR production report — policy -26 means 2025 production data
            const cyMatch = policyNum.match(/-(\d{2})$/);
            const policyYear = cyMatch ? 2000 + parseInt(cyMatch[1]) : 2026;
            const cropYear = policyYear - 1; // Prior year production

            results.push({
              _isProd: true,
              aip: 'NAU', policyNumber: policyNum, insuredName: insName,
              county, crop, measure, state: stateAbbr,
              production: netProd, acres: netAcres,
              totalProduction: totalProd, totalAcres,
              cropYear,
              yieldPerAcre: netAcres > 0 ? Math.round(netProd / netAcres * 10) / 10 : null,
            });
          });
          return results;
        };

        // ── Parse R&H 2025 Total Production report ──
        const parseRH_PROD = (rows) => {
          const results = [];
          rows.forEach(r => {
            const crop = normCrop(r['Commodity']);
            if (!crop) return;
            const county = String(r['County']||'').trim().toUpperCase();
            const policyNum = String(r['Policy']||'').trim();
            const insName = String(r['Name']||'').trim();
            const measure = String(r['Unit of Measure']||r['Unit Structure']||'BU').trim().toUpperCase();
            const acres = parseFloat(String(r['Acres']||'0').replace(/,/g,'')) || 0;
            const totalProd = parseFloat(String(r['Total Production']||'0').replace(/,/g,'')) || 0;
            const netProd = parseFloat(String(r['Net Production*']||r['Net Production']||'0').replace(/,/g,'')) || 0;
            if (acres <= 0) return;

            results.push({
              _isProd: true,
              aip: 'R&H', policyNumber: policyNum, insuredName: insName,
              county, crop, measure, state: 'NY', // R&H report doesn't have state column; all are NY/PA/OH
              production: netProd || totalProd, acres,
              totalProduction: totalProd, totalAcres: acres,
              cropYear: 2025,
              yieldPerAcre: acres > 0 ? Math.round((netProd || totalProd) / acres * 10) / 10 : null,
            });
          });
          return results;
        };

        // ── Parse R&H Data Set Builder (detailed with Type + Practice) ──
        const parseRH = (rows) => {
          const results = [];
          rows.forEach(r => {
            const crop = String(r['Crop']||'').toUpperCase().trim();
            if (!crop) return;
            const planRaw = String(r['Plan']||'');
            const plan = RH_PLAN_MAP(planRaw);
            if (!plan) return; // skip DRP/LRP/PRF
            const practiceRaw = String(r['Practice Name']||'');
            const practice = RH_PRACTICE_MAP(practiceRaw);
            if (practice === null && plan !== 'SCO-RP' && plan !== 'ECO-RP' && plan !== 'SCO-YP' && plan !== 'ECO-YP') return; // index interval → skip
            const typeRaw = String(r['Type']||'');
            const type = RH_TYPE_MAP[typeRaw] || typeRaw || '';
            const county = String(r['County']||'').toUpperCase().trim();
            const policyNum = String(r['Policy Number']||'').trim();
            const sortName = String(r['Sort Name']||'').trim();
            const levelStr = String(r['Level']||'0').replace('%','').replace('CAT','');
            const level = parseFloat(levelStr) / 100 || 0;
            const rawYield = String(r['Approved Yield']||'').replace(/,/g,'');
            const aphYield = (rawYield === '' || rawYield === 'NaN' || rawYield === undefined || isNaN(parseFloat(rawYield)) || parseFloat(rawYield) === 0)
              ? null : parseFloat(rawYield);
            const yieldLineId = r['Yield Line ID'];
            const rawPrem = parseFloat(String(r['Premium']||'').replace(/,/g,''));
            const rawAdj  = parseFloat(String(r['Adjusted Premium']||'').replace(/,/g,''));
            const totalPremium = (!isNaN(rawPrem) && rawPrem > 0) ? rawPrem : null;
            const netPremium   = (!isNaN(rawAdj) && rawAdj > 0) ? rawAdj : null;
            const subsidy      = (totalPremium != null && netPremium != null) ? totalPremium - netPremium : null;
            const rawAcres = parseFloat(String(r['Acres']||'').replace(/,/g,''));
            const acres    = (!isNaN(rawAcres) && rawAcres > 0) ? rawAcres : null;

            // SCO/ECO rows often have no practice/type/yield — that's fine
            results.push({
              aip: 'R&H', policyNumber: policyNum, insuredName: sortName,
              county, crop, plan, type: type || '', practice: practice || 'Non-Irrigated',
              unitNumber: yieldLineId ? String(yieldLineId) : '',
              coverageLevel: level, aphYield, price: null,
              totalPremium, subsidy, netPremium, acres,
              ppLevel: null, ppLiabilityPerAcre: null, share: 1,
              isWheat: FALL_CROPS_SKIP.includes(crop), isFallCrop: FALL_CROPS_KEEP.includes(crop),
            });
          });
          return results;
        };

        // ── Parse R&H MPCI Prevented Planting Acres report ──
        const parseRH_PP = (rows) => {
          const results = [];
          rows.forEach(r => {
            const cropRaw = String(r['Crop']||'').trim();
            // Skip Type: and Practice: sub-rows — these are detail breakdowns
            if (cropRaw.startsWith('Type:') || cropRaw.startsWith('Practice:')) return;
            const crop = normCrop(cropRaw);
            if (!crop) return;
            const county = String(r['County']||'').trim().toUpperCase();
            const policyNum = String(r['Policy']||'').trim();
            const insName = String(r['Name']||'').trim();
            const plan = String(r['Plan']||'').trim().toUpperCase();
            if (!policyNum || !insName) return;
            const maxAcres = parseFloat(String(r['Max Eligible Acres']||'0').replace(/,/g,'')) || 0;
            const remainAcres = parseFloat(String(r['Remaining Eligible Acres']||'0').replace(/,/g,'')) || 0;

            results.push({
              _isPP: true,
              aip: 'R&H', policyNumber: policyNum, insuredName: insName,
              county, crop, plan: plan || 'RP',
              maxAcres, remainingAcres: remainAcres,
            });
          });
          return results;
        };

        // ── Parse R&H LRP export ──
        const parseLRP = (rows) => {
          const results = [];
          rows.forEach(r => {
            const policyType = String(r['Livestock Policy']||'').toUpperCase().trim();
            if (policyType !== 'LRP') return;
            const policyNum = String(r['Policy Number']||'').trim();
            const sortName = String(r['Sort Name']||'').trim();
            if (!policyNum || !sortName) return;
            // Skip policy-header rows (no endorsement data)
            const premium = parseFloat(String(r['Premium']||'0').replace(/[$,]/g,'')) || 0;
            const salesDate = String(r['Sales Effective Date']||'').trim();
            if (!premium && !salesDate) return; // header row, no endorsement data
            const adjPremium = parseFloat(String(r['Adjusted Premium']||'0').replace(/[$,]/g,'')) || 0;
            const indemnity = parseFloat(String(r['Payable Indemnity']||'0').replace(/[$,]/g,'')) || null;
            const coverageLevel = parseFloat(String(r['Coverage Level']||'0').replace(/[%,]/g,'')) || null;
            const coveragePrice = parseFloat(String(r['LRP Coverage Price']||'0').replace(/[$,]/g,'')) || null;
            const endDate = String(r['LRP End Date']||'').trim();
            const weeksOut = parseFloat(String(r['LRP Endorsement Length']||'0').replace(/,/g,'')) || null;
            const head = parseFloat(String(r['LRP Number Of Head']||'0').replace(/,/g,'')) || null;
            const targetWeight = parseFloat(String(r['LRP Target Weight']||'0').replace(/,/g,'')) || null;
            // Compute subsidy = premium - adjusted premium
            const subsidy = (premium && adjPremium) ? Math.round((premium - adjPremium) * 100) / 100 : null;
            // Compute insured value from coverage price * head * target weight * 100 (cwt)
            const insuredValue = (coveragePrice && head && targetWeight) ? Math.round(coveragePrice * head * targetWeight * 100) / 100 : null;

            results.push({
              _isLRP: true,
              aip: 'R&H', policyNumber: policyNum, insuredName: sortName,
              salesDate, endDate, weeksOut, head, targetWeight,
              coveragePrice, insuredValue, premium, producerPremium: adjPremium,
              subsidy, payableIndemnity: indemnity, coverageLevel,
            });
          });
          return results;
        };

        // ── Group parsed rows by policy for preview ──
        // Normalize policy numbers for matching: strip leading zeros from numeric portion
        // e.g. "MP-0843027" → "MP-843027", "NY-944-6083379-26" stays as-is (no leading zeros)
        const normPolicyNum = (pn) => {
          if (!pn) return '';
          return pn.toUpperCase().replace(/(\D)0+(\d)/g, '$1$2');
        };
        // Strip year suffix for cross-year matching (production data uses -25, policies use -26)
        const stripYearSuffix = (pn) => {
          if (!pn) return '';
          return pn.toUpperCase().replace(/-\d{2}$/, '');
        };

        const buildPreview = async (parsedRows) => {
          // Fetch all policies from Supabase for correct policy_number → policy_id mapping
          // The farmer objects don't reliably map policy numbers to policy IDs
          const allPolicies = await sbFetch('policies', { select: 'id,policy_number,farmer_id,crop_year,aip' });
          const farmerRows = await sbFetch('farmers', { select: 'id,name' });
          const farmerNameById = {};
          farmerRows.forEach(f => { farmerNameById[f.id] = f.name; });

          // Build lookup: normalized policy number → { policyId, farmerName, ... }
          // IMPORTANT: Process policies sorted by crop_year ASC so that 2026 overwrites older years.
          // This ensures we always match to the current crop year policy when multiple exist.
          const policyLookup = {};
          const sorted = [...allPolicies].sort((a,b) => (a.crop_year||0) - (b.crop_year||0));
          sorted.forEach(p => {
            if (!p.policy_number) return;
            const entry = {
              policyId: p.id,
              farmerName: farmerNameById[p.farmer_id] || '?',
              farmerId: p.farmer_id,
              aip: p.aip || '',
              cropYear: p.crop_year,
            };
            // Later (higher) crop years overwrite earlier ones — 2026 wins over 2025
            policyLookup[p.policy_number.toUpperCase()] = entry;
            policyLookup[normPolicyNum(p.policy_number)] = entry;
            // Also index by stripped year suffix for cross-year matching (e.g. production -25 → policy -26)
            policyLookup[stripYearSuffix(p.policy_number)] = entry;
          });

          // Build a name-based lookup as fallback for production data
          // (farmer may have different policy numbers between crop years)
          const nameLookup = {};
          sorted.forEach(p => {
            const fName = (farmerNameById[p.farmer_id] || '').trim().toLowerCase();
            if (!fName) return;
            const entry = {
              policyId: p.id,
              farmerName: farmerNameById[p.farmer_id] || '?',
              farmerId: p.farmer_id,
              aip: p.aip || '',
              cropYear: p.crop_year,
            };
            // Later crop years overwrite — 2026 wins
            nameLookup[fName] = entry;
          });

          // Group rows by policyNumber
          const byPolicy = {};
          parsedRows.forEach(r => {
            const key = r.policyNumber.toUpperCase();
            if (!byPolicy[key]) byPolicy[key] = { policyNumber: r.policyNumber, aip: r.aip, insuredName: r.insuredName, rows: [], match: null };
            byPolicy[key].rows.push(r);
          });

          // Match to app — try exact first, then normalized (strips leading zeros), then year-stripped,
          // then fall back to farmer name matching for production data
          Object.values(byPolicy).forEach(g => {
            const lookup = policyLookup[g.policyNumber.toUpperCase()]
              || policyLookup[normPolicyNum(g.policyNumber)]
              || policyLookup[stripYearSuffix(g.policyNumber)];
            if (lookup) {
              g.match = lookup;
              g.status = 'matched';
            } else {
              // Fallback: try matching by farmer name (useful for production data with different policy numbers)
              const nameMatch = nameLookup[(g.insuredName||'').trim().toLowerCase()];
              if (nameMatch) {
                g.match = nameMatch;
                g.status = 'matched';
              } else {
                g.status = 'unmatched';
              }
            }
          });

          return Object.values(byPolicy).sort((a,b) => (a.status==='matched'?0:1) - (b.status==='matched'?0:1) || (a.insuredName||'').localeCompare(b.insuredName||''));
        };

        return (
          <Card>
            <SectionTitle>POLICY DATA IMPORT — EXCEL</SectionTitle>
            <p style={{margin:'0 0 16px',color:'#60735f',fontSize:13}}>
              Upload the <strong>NAU Prevented Plant Liability</strong> report or <strong>Rain &amp; Hail Data Set Builder</strong> export.
              The importer auto-detects the AIP format, matches by policy number, and replaces crop coverage and PP data.
              Wheat is skipped (already set for 2026). Barley and oats import as fall crops. Re-upload anytime — no duplicates.
            </p>

            {/* File drop zone */}
            <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:14}}>
              {[
                {label:'FILE 1', file:xlsxFile, setFile:(f)=>{setXlsxFile(f);setXlsxPreview(null);setXlsxStatus(null);setXlsxDetected(null);}},
                {label:'FILE 2 (optional)', file:xlsxFile2, setFile:(f)=>{setXlsxFile2(f);setXlsxPreview(null);setXlsxStatus(null);setXlsxDetected(null);}},
              ].map(({label,file,setFile},i) => (
                <div key={i} style={{flex:'1 1 260px',background:'#f8faf8',border:'1px solid #c8d8c8',borderRadius:8,padding:'12px 16px'}}>
                  <div style={{fontWeight:700,fontSize:11,color:'#4a6e4a',marginBottom:6,letterSpacing:'0.08em',textTransform:'uppercase'}}>{label}</div>
                  <input type="file" accept=".xlsx,.xls" onChange={e=>setFile(e.target.files[0]||null)} style={{fontSize:12}} />
                  {file && <div style={{fontSize:11,color:'#4a6e4a',marginTop:4}}>📎 {file.name}</div>}
                </div>
              ))}
            </div>

            {/* Detected format badge */}
            {xlsxDetected && (
              <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:12,flexWrap:'wrap'}}>
                {[xlsxDetected.file1, xlsxDetected.file2].filter(Boolean).map((d,i) => (
                  <div key={i} style={{fontSize:12,fontWeight:700,padding:'4px 12px',borderRadius:6,
                    background: d.fmt==='NAU_PPL'?'#e8f5e9':d.fmt==='NAU_PROD'?'#e0ecf8':(d.fmt==='RH_PROD'||d.fmt==='RH_PROD_TITLE')?'#e0ecf8':d.fmt==='RH_LRP'?'#f0e8f8':d.fmt.startsWith('RH')?'#e8eef8':'#fdf0e0',
                    color: d.fmt==='NAU_PPL'?'#2d4a2d':d.fmt==='NAU_PROD'?'#1a4a7a':(d.fmt==='RH_PROD'||d.fmt==='RH_PROD_TITLE')?'#1a4a7a':d.fmt==='RH_LRP'?'#5a2d6a':d.fmt.startsWith('RH')?'#2d3a6a':'#6a3a2d'}}>
                    {d.filename}: {d.fmt === 'NAU_PPL' ? 'NAU' : d.fmt === 'NAU_PROD' ? 'NAU' : d.fmt === 'RH_PROD' || d.fmt === 'RH_PROD_TITLE' ? 'Rain & Hail' : d.fmt === 'RH_LRP' ? 'Rain & Hail' : d.fmt.startsWith('RH') ? 'Rain & Hail' : d.fmt} — {d.rowCount} data rows, {d.matchedPolicies} policies matched, {d.crops.join(', ')}
                    <div style={{fontSize:10,fontWeight:800,marginTop:3,padding:'2px 8px',borderRadius:3,display:'inline-block',
                      background: (d.fmt==='NAU_PROD'||d.fmt==='RH_PROD'||d.fmt==='RH_PROD_TITLE') ? '#e0ecf8' : d.fmt==='RH_LRP' ? '#f0e8f8' : (d.fmt==='RH_PP'||d.fmt==='RH_PP_TITLE') ? '#f0e8d8' : '#e8f5e9',
                      color: (d.fmt==='NAU_PROD'||d.fmt==='RH_PROD'||d.fmt==='RH_PROD_TITLE') ? '#1a4a7a' : d.fmt==='RH_LRP' ? '#5a2d6a' : (d.fmt==='RH_PP'||d.fmt==='RH_PP_TITLE') ? '#7a5a2d' : '#2d4a2d'}}>
                      → {d.fmt === 'NAU_PPL' ? 'Updates Farmer Cards: Spring Crop APH Yields + PP Data' :
                         d.fmt === 'RH_DSB' || d.fmt === 'RH_SIMPLE' ? 'Updates Farmer Cards: Spring Crop APH Yields' :
                         d.fmt === 'NAU_PROD' || d.fmt === 'RH_PROD' || d.fmt === 'RH_PROD_TITLE' ? `Updates ${CURRENT_CROP_YEAR - 1} Actual Yield History Dashboard` :
                         d.fmt === 'RH_LRP' ? 'Updates LRP Endorsement Pages' :
                         d.fmt === 'RH_PP' || d.fmt === 'RH_PP_TITLE' ? 'Updates Farmer Cards: Prevent Plant Eligibility' :
                         'Unknown destination'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {xlsxStatus && (
              <div style={{padding:'10px 14px',background:xlsxStatus.ok?'#e8f5e9':'#fdecea',
                color:xlsxStatus.ok?'#2d4a2d':'#c62828',borderRadius:6,fontSize:13,fontWeight:600,
                marginBottom:12,whiteSpace:'pre-wrap'}}>
                {xlsxStatus.msg}
              </div>
            )}

            {/* Parse button */}
            {!xlsxPreview && (
              <button onClick={async () => {
                if (!xlsxFile && !xlsxFile2) { setXlsxStatus({ok:false,msg:'Select at least one file.'}); return; }
                setXlsxStatus({ok:true,msg:'Reading file(s)...'});
                try {
                  let allParsed = [];
                  const detectedInfo = {};
                  for (const [idx, file] of [xlsxFile, xlsxFile2].filter(Boolean).entries()) {
                    const d = await loadXlsx(file);
                    const fmt = detectFormat(d.headers);
                    let parsed = [];
                    if (fmt === 'NAU_PPL') parsed = parseNAU(d.rows);
                    else if (fmt === 'NAU_PROD') parsed = parseNAU_PROD(d.rows);
                    else if (fmt === 'RH_DSB' || fmt === 'RH_SIMPLE') parsed = parseRH(d.rows);
                    else if (fmt === 'RH_LRP') parsed = parseLRP(d.rows);
                    else if (fmt === 'RH_PP_TITLE' || fmt === 'RH_PP') {
                      // R&H PP report has a title row "MPCI Prevented Planting Acres"
                      let ppData = d.rows;
                      if (fmt === 'RH_PP_TITLE') {
                        const buf2 = await file.arrayBuffer();
                        const wb2 = window.XLSX.read(buf2, {cellDates:false, raw:false});
                        const ws2 = wb2.Sheets[wb2.SheetNames[0]];
                        const allRows = window.XLSX.utils.sheet_to_json(ws2, {defval:'', raw:false, header:1});
                        if (allRows.length > 2) {
                          const realHeaders = allRows[1].map(h => String(h||'').trim());
                          ppData = allRows.slice(2).map(row => {
                            const obj = {};
                            realHeaders.forEach((h,i) => { obj[h] = (row[i] === null || row[i] === undefined) ? '' : (typeof row[i] === 'object') ? String(row[i]) : row[i]; });
                            return obj;
                          }).filter(r => r['Policy'] || r['Name']);
                        }
                      }
                      parsed = parseRH_PP(ppData);
                    }
                    else if (fmt === 'RH_PROD_TITLE' || fmt === 'RH_PROD') {
                      // R&H production report has a title row — first "row" from SheetJS is the real header
                      // The first data row's keys are the title; the values of row[0] are the real headers
                      let prodData = d.rows;
                      let actualFmt = fmt;
                      if (fmt === 'RH_PROD_TITLE') {
                        // Re-read with header row offset: row 1 is title, row 2 is headers
                        const buf2 = await file.arrayBuffer();
                        const wb2 = window.XLSX.read(buf2, {cellDates:false, raw:false});
                        const ws2 = wb2.Sheets[wb2.SheetNames[0]];
                        const allRows = window.XLSX.utils.sheet_to_json(ws2, {defval:'', raw:false, header:1});
                        // Row 0 = title, Row 1 = real headers, Row 2+ = data
                        if (allRows.length > 2) {
                          const realHeaders = allRows[1].map(h => String(h||'').trim());
                          prodData = allRows.slice(2).map(row => {
                            const obj = {};
                            realHeaders.forEach((h,i) => { obj[h] = (row[i] === null || row[i] === undefined) ? '' : (typeof row[i] === 'object') ? String(row[i]) : row[i]; });
                            return obj;
                          }).filter(r => r['Policy'] || r['Name']); // skip empty rows
                        }
                        actualFmt = 'RH_PROD';
                      }
                      parsed = parseRH_PROD(prodData);
                    }
                    else { setXlsxStatus({ok:false,msg:`Could not detect format for ${file.name}. Expected NAU PPL, NAU Production, R&H DSB, R&H LRP, R&H PP, or R&H Production export.`}); return; }

                    const isLRP = fmt === 'RH_LRP';
                    const isProd = fmt === 'NAU_PROD' || fmt === 'RH_PROD' || fmt === 'RH_PROD_TITLE';
                    const isPP = fmt === 'RH_PP' || fmt === 'RH_PP_TITLE';
                    const crops = isLRP ? ['LRP'] : isProd ? [...new Set(parsed.map(r => r.crop).filter(Boolean))].sort() : [...new Set(parsed.map(r => r.crop).filter(Boolean))].sort();
                    const policies = [...new Set(parsed.map(r => r.policyNumber))];
                    // Quick match count
                    const matchCount = policies.filter(pn => farmers.some(f => (f._allPolicyNums||[]).some(p => p.toUpperCase()===pn.toUpperCase() || normPolicyNum(p)===normPolicyNum(pn)) || (f.policyNumber||'').toUpperCase()===pn.toUpperCase() || normPolicyNum(f.policyNumber||'')===normPolicyNum(pn))).length;
                    detectedInfo['file'+(idx+1)] = { fmt, filename: file.name, rowCount: parsed.length, crops, matchedPolicies: matchCount, totalPolicies: policies.length };
                    allParsed = allParsed.concat(parsed);
                  }
                  setXlsxDetected(detectedInfo);
                  const preview = await buildPreview(allParsed);
                  const matched = preview.filter(r=>r.status==='matched').length;
                  const unmatched = preview.filter(r=>r.status==='unmatched').length;
                  const totalRows = preview.reduce((s,g)=>s+g.rows.length, 0);
                  setXlsxPreview(preview);
                  setXlsxStatus({ok:true, msg:`Found ${totalRows} data rows across ${preview.length} policies — ${matched} matched, ${unmatched} unmatched (will skip).`});
                } catch(err) {
                  setXlsxStatus({ok:false,msg:'Error: '+err.message});
                }
              }} style={{padding:'10px 28px',background:'#2d4a2d',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:14,cursor:'pointer'}}>
                🔍 Detect &amp; Preview
              </button>
            )}

            {/* Preview table */}
            {xlsxPreview && (
              <div>
                <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:10,flexWrap:'wrap'}}>
                  <span style={{fontWeight:700,fontSize:14,color:'#1a2e1a'}}>Preview — {xlsxPreview.length} policies</span>
                  <span style={{fontSize:12,color:'#4a6e4a'}}>{xlsxPreview.filter(r=>r.status==='matched').length} matched</span>
                  <span style={{fontSize:12,color:'#c62828'}}>{xlsxPreview.filter(r=>r.status==='unmatched').length} unmatched</span>
                  {xlsxPreview.filter(r=>r.status==='unmatched').length > 0 && !xlsxImporting && (
                    <button onClick={async () => {
                      const unmatched = xlsxPreview.filter(g => g.status === 'unmatched');
                      if (!unmatched.length) return;
                      const confirmMsg = `Create ${unmatched.length} new farmer(s) and their policies in CloverAg?\n\n${unmatched.map(g => `• ${g.insuredName} (${g.policyNumber})`).join('\n')}\n\nThis will create farmer + policy rows so the import can match them.`;
                      if (!window.confirm(confirmMsg)) return;
                      setXlsxStatus({ok:true, msg:`Creating ${unmatched.length} new farmers...`});
                      let created = 0;
                      const createErrors = [];
                      for (const g of unmatched) {
                        try {
                          // Resolve to the spelling this customer is already
                          // stored under before creating anything — an exact
                          // match alone treats "ERNEST L GATES & SONS LLC" as a
                          // new customer and splits their policies in two.
                          const storedName = await resolveExistingFarmerName(g.insuredName) || g.insuredName;
                          const existing = await sbFetch('farmers', { filters: [`name=eq.${encodeURIComponent(storedName)}`] });
                          let farmerId;
                          if (existing && existing.length > 0) {
                            farmerId = existing[0].id;
                          } else {
                            // Create new farmer
                            const newFarmer = await sbInsert('farmers', [{ name: g.insuredName }]);
                            if (newFarmer && newFarmer.length > 0) {
                              farmerId = newFarmer[0].id;
                            } else {
                              // Fetch the just-created farmer
                              const refetch = await sbFetch('farmers', { filters: [`name=eq.${encodeURIComponent(g.insuredName)}`] });
                              farmerId = refetch?.[0]?.id;
                            }
                          }
                          if (!farmerId) { createErrors.push(`${g.insuredName}: could not get farmer ID`); continue; }
                          // Determine AIP and state from the data
                          const aip = g.aip || 'R&H';
                          const stateGuess = g.policyNumber.startsWith('MP-') ? 'NY' : g.policyNumber.substring(0, 2);
                          const insurer = aip === 'NAU' ? 'NAU Country Insurance Company' : 'ACE PROPERTY AND CASUALTY INSURANCE COMPANY';
                          // Check if policy already exists
                          const existingPolicy = await sbFetch('policies', { filters: [`policy_number=eq.${encodeURIComponent(g.policyNumber)}`] });
                          if (!existingPolicy || existingPolicy.length === 0) {
                            await sbInsert('policies', [{
                              farmer_id: farmerId,
                              policy_number: g.policyNumber,
                              crop_year: CURRENT_CROP_YEAR,   // the book's year, not a fixed 2026
                              aip: aip === 'R&H' ? 'Rain & Hail' : aip,
                              state: stateGuess,
                              insurer,
                              agent_name: null,
                              agency_name: 'SPARTAN INSURANCE AGENCY LLC',
                            }]);
                          }
                          created++;
                        } catch (err) {
                          createErrors.push(`${g.insuredName}: ${err.message}`);
                        }
                      }
                      // Re-run preview to pick up the new matches
                      const allParsed = xlsxPreview.flatMap(g => g.rows);
                      const preview = await buildPreview(allParsed);
                      setXlsxPreview(preview);
                      const matched = preview.filter(r=>r.status==='matched').length;
                      const stillUnmatched = preview.filter(r=>r.status==='unmatched').length;
                      setXlsxStatus({ok: createErrors.length === 0, msg: [
                        `Created ${created} new farmer(s).`,
                        createErrors.length > 0 ? `Errors: ${createErrors.join('; ')}` : '',
                        `Preview refreshed: ${matched} matched, ${stillUnmatched} unmatched.`
                      ].filter(Boolean).join('\n')});
                    }} style={{padding:'4px 12px',background:'#e8f5e9',border:'1px solid #4a7a2a',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer',color:'#2d4a2d'}}>
                      ➕ Auto-Create {xlsxPreview.filter(r=>r.status==='unmatched').length} Missing Farmers
                    </button>
                  )}
                  <button onClick={()=>{setXlsxPreview(null);setXlsxStatus(null);setXlsxDetected(null);}} style={{marginLeft:'auto',padding:'4px 12px',background:'none',border:'1px solid #c8d8c8',borderRadius:5,fontSize:12,cursor:'pointer',color:'#60735f'}}>✕ Reset</button>
                </div>
                <div style={{overflowX:'auto',marginBottom:14,maxHeight:500,overflowY:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead>
                      <tr style={{background:'#f4f8f4',position:'sticky',top:0}}>
                        {['Status','AIP','Policy #','Insured','App Match','Crops','Units','With APH','No APH'].map(h => (
                          <th key={h} style={{padding:'6px 10px',textAlign:'left',borderBottom:'2px solid #c8d8c8',fontSize:11,color:'#4a6e4a',fontWeight:700}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {xlsxPreview.map((g,i) => {
                        const isLRP = g.rows.some(r => r._isLRP);
                        const isProd = g.rows.some(r => r._isProd);
                        const isPP = g.rows.some(r => r._isPP);
                        const basePlans = g.rows.filter(r => ['RP','YP','APH'].includes(r.plan) && !r.isWheat && !r._isPP);
                        const wheatRows = g.rows.filter(r => r.isWheat);
                        const lrpRows = g.rows.filter(r => r._isLRP);
                        const prodRows = g.rows.filter(r => r._isProd);
                        const ppRows = g.rows.filter(r => r._isPP);
                        const withYield = basePlans.filter(r => r.aphYield != null).length;
                        const noYield = basePlans.filter(r => r.aphYield == null).length;
                        const crops = isLRP ? 'LRP' : isProd ? [...new Set(prodRows.map(r => r.crop))].join(', ') : isPP ? [...new Set(ppRows.map(r => r.crop))].join(', ') : [...new Set(basePlans.map(r => r.crop))].join(', ');
                        const isWheatOnly = !isLRP && !isProd && !isPP && basePlans.length === 0 && wheatRows.length > 0;
                        const totalHead = lrpRows.reduce((s,r) => s + (r.head||0), 0);
                        const totalProdAcres = prodRows.reduce((s,r) => s + (r.acres||0), 0);
                        const totalPPAcres = ppRows.reduce((s,r) => s + (r.maxAcres||0), 0);
                        return (
                          <tr key={i} style={{borderBottom:'1px solid #e8e8e8',background:g.status==='unmatched'?'#fff8f8':isWheatOnly?'#faf8f0':isLRP?'#f4f0f8':isProd?'#f0f4f8':isPP?'#f8f4e8':'#fff'}}>
                            <td style={{padding:'5px 10px'}}><span style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:4,background:g.status==='matched'?'#e8f5e9':'#fdecea',color:g.status==='matched'?'#2d4a2d':'#c62828'}}>{g.status==='matched'?'✓':'✕'}</span></td>
                            <td style={{padding:'5px 10px'}}><span style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:4,background:isLRP?'#f0e8f8':isProd?'#e0ecf8':isPP?'#f0e8d8':g.aip==='NAU'?'#e8f5e9':'#e8eef8',color:isLRP?'#5a2d6a':isProd?'#1a4a7a':isPP?'#7a5a2d':g.aip==='NAU'?'#2d4a2d':'#2d3a6a'}}>{isLRP?'LRP':isProd?'2025 PROD':isPP?'PP':g.aip}</span></td>
                            <td style={{padding:'5px 10px',fontVariantNumeric: "tabular-nums",fontSize:11}}>{g.policyNumber}</td>
                            <td style={{padding:'5px 10px',fontWeight:600,fontSize:11,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g.insuredName}</td>
                            <td style={{padding:'5px 10px',fontSize:11,color:g.match?'#2d4a2d':'#c62828'}}>{g.match ? g.match.farmerName : '—'}</td>
                            <td style={{padding:'5px 10px',fontSize:11}}>{isWheatOnly ? <span style={{color:'#8a7a40',fontStyle:'italic'}}>WHEAT only (cleanup)</span> : isLRP ? <span style={{color:'#5a2d6a'}}>🐄 {lrpRows.length} endorsements · {totalHead} head</span> : isProd ? <span style={{color:'#1a4a7a'}}>📊 {prodRows.length} rows · {totalProdAcres.toLocaleString()} ac</span> : isPP ? <span style={{color:'#7a5a2d'}}>🌾 {ppRows.length} crops · {totalPPAcres.toLocaleString()} max ac</span> : crops}</td>
                            <td style={{padding:'5px 10px',fontVariantNumeric: "tabular-nums",fontSize:11}}>{isLRP ? lrpRows.length : isProd ? prodRows.length : isPP ? ppRows.length : basePlans.length}</td>
                            <td style={{padding:'5px 10px',fontVariantNumeric: "tabular-nums",fontSize:11,color:'#2d4a2d'}}>{isLRP || isProd || isPP ? '—' : withYield}</td>
                            <td style={{padding:'5px 10px',fontVariantNumeric: "tabular-nums",fontSize:11,color:noYield>0?'#c8860a':'#9a9a8a'}}>{isLRP || isProd || isPP ? '—' : (noYield || '—')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Summary bar */}
                {(() => {
                  const m = xlsxPreview.filter(r=>r.status==='matched');
                  const allBase = m.flatMap(g => g.rows.filter(r => ['RP','YP','APH'].includes(r.plan) && !r.isWheat));
                  const allLrp = m.flatMap(g => g.rows.filter(r => r._isLRP));
                  const withYield = allBase.filter(r => r.aphYield != null).length;
                  const noYield = allBase.filter(r => r.aphYield == null).length;
                  const crops = [...new Set(allBase.map(r=>r.crop).filter(Boolean))].sort();
                  const scoEco = m.flatMap(g => g.rows.filter(r => ((r.plan||'').includes('SCO') || (r.plan||'').includes('ECO')) && !r.isWheat)).length;
                  const wheatOnlyCount = m.filter(g => g.rows.every(r => r.isWheat)).length;
                  const lrpPolicies = m.filter(g => g.rows.some(r => r._isLRP)).length;
                  const lrpHead = allLrp.reduce((s,r) => s + (r.head||0), 0);
                  return (
                    <div style={{display:'flex',gap:20,flexWrap:'wrap',padding:'10px 14px',background:'#f0f8f0',borderRadius:7,marginBottom:14,fontSize:12,fontWeight:700,color:'#2d4a2d'}}>
                      <span>{m.length} policies to update</span>
                      {crops.length > 0 && <span>{crops.join(' · ')}</span>}
                      {withYield > 0 && <span>{withYield} units with APH</span>}
                      {noYield > 0 && <span style={{color:'#c8860a'}}>{noYield} units pending APH</span>}
                      {scoEco > 0 && <span style={{color:'#2d3a6a'}}>{scoEco} SCO/ECO rows</span>}
                      {wheatOnlyCount > 0 && <span style={{color:'#8a7a40'}}>{wheatOnlyCount} wheat-only policies (stale data cleanup)</span>}
                      {lrpPolicies > 0 && <span style={{color:'#5a2d6a'}}>🐄 {allLrp.length} LRP endorsements · {lrpHead} head across {lrpPolicies} policies</span>}
                      {(() => { const ppPolicies = m.filter(g => g.rows.some(r => r._isPP)); const allPP = m.flatMap(g => g.rows.filter(r => r._isPP)); const ppAcres = allPP.reduce((s,r) => s + (r.maxAcres||0), 0); return ppPolicies.length > 0 ? <span style={{color:'#7a5a2d'}}>🌾 {allPP.length} PP rows · {ppAcres.toLocaleString()} max ac across {ppPolicies.length} policies</span> : null; })()}
                      {(() => { const prodPolicies = m.filter(g => g.rows.some(r => r._isProd)); const allProd = m.flatMap(g => g.rows.filter(r => r._isProd)); const prodAcres = allProd.reduce((s,r) => s + (r.acres||0), 0); return prodPolicies.length > 0 ? <span style={{color:'#1a4a7a'}}>📊 {allProd.length} production rows · {prodAcres.toLocaleString()} ac across {prodPolicies.length} policies</span> : null; })()}
                    </div>
                  );
                })()}

                {/* Destination summary — shows exactly where data will be written */}
                {(() => {
                  const m = xlsxPreview.filter(r=>r.status==='matched');
                  const hasCropData = m.some(g => g.rows.some(r => !r._isLRP && !r._isProd && !r._isPP && (r.plan || r.isWheat)));
                  const hasLrpData = m.some(g => g.rows.some(r => r._isLRP));
                  const hasProdData = m.some(g => g.rows.some(r => r._isProd));
                  const hasNauPpData = m.some(g => g.rows.some(r => !r._isLRP && !r._isProd && !r._isPP && r.ppLevel != null));
                  const hasRhPpData = m.some(g => g.rows.some(r => r._isPP));
                  if (!hasCropData && !hasLrpData && !hasProdData && !hasRhPpData) return null;
                  return (
                    <div style={{padding:'10px 14px',background:'#f8f8ff',border:'1px solid #d0d8e8',borderRadius:7,marginBottom:12,fontSize:12}}>
                      <div style={{fontWeight:800,fontSize:11,color:'#4a4a6a',marginBottom:6,letterSpacing:'0.06em'}}>📍 IMPORT DESTINATIONS</div>
                      {hasCropData && <div style={{color:'#2d4a2d',marginBottom:3}}>✅ <strong>Farmer Cards → Spring Crop APH Yields</strong> — writes to <code style={{fontSize:10,background:'#e8f5e9',padding:'1px 4px',borderRadius:3}}>crop_coverage</code> table</div>}
                      {(hasNauPpData || hasRhPpData) && <div style={{color:'#7a5a2d',marginBottom:3}}>✅ <strong>Farmer Cards → Prevent Plant Eligibility</strong> — writes to <code style={{fontSize:10,background:'#f0e8d8',padding:'1px 4px',borderRadius:3}}>pp_eligibility</code> table</div>}
                      {hasLrpData && <div style={{color:'#5a2d6a',marginBottom:3}}>✅ <strong>LRP Endorsement Pages</strong> — writes to <code style={{fontSize:10,background:'#f0e8f8',padding:'1px 4px',borderRadius:3}}>lrp_endorsements</code> table</div>}
                      {hasProdData && <div style={{color:'#1a4a7a',marginBottom:3}}>✅ <strong>{CURRENT_CROP_YEAR - 1} Actual Yield History Dashboard</strong> — writes to <code style={{fontSize:10,background:'#e0ecf8',padding:'1px 4px',borderRadius:3}}>production_history</code> table</div>}
                    </div>
                  );
                })()}

                {/* Import button */}
                <button disabled={xlsxImporting} onClick={async () => {
                  const toImport = xlsxPreview.filter(g => g.status==='matched' && g.match);
                  if (!toImport.length) { setXlsxStatus({ok:false,msg:'No matched policies to import.'}); return; }
                  setXlsxImporting(true);
                  setXlsxStatus({ok:true, msg:`Importing 0 / ${toImport.length} policies...`});
                  let done=0; const errors=[]; let totalCovRows=0; let totalPpRows=0; let totalLrpRows=0; let totalProdRows=0;

                  // ── Smart cleanup for production data ──
                  // Delete only production_history rows from the SAME source type being imported.
                  // This prevents NAU imports from wiping R&H data and vice versa,
                  // and prevents a second agent's R&H file from wiping the first agent's NAU data.
                  // Also cleans up old Dropbox-extracted data for the same farmers being imported.
                  const allProdGroups = toImport.filter(g => g.rows.some(r => r._isProd));
                  if (allProdGroups.length > 0) {
                    const prodCropYears = [...new Set(allProdGroups.flatMap(g => g.rows.filter(r => r._isProd).map(r => r.cropYear || CURRENT_CROP_YEAR - 1)))];   // fallback: last completed harvest, not a fixed 2025
                    // Determine which source_file values this import will write
                    const sourceFiles = [...new Set(allProdGroups.map(g => 
                      g.aip === 'R&H' ? 'R&H 2025 Total Production Excel' : 'NAU Prior Year Net Production Excel'
                    ))];
                    for (const cy of prodCropYears) {
                      // Delete rows from same Excel source across ALL crop years (handles wrong-year re-imports)
                      for (const sf of sourceFiles) {
                        await sbDelete('production_history', `source_file=eq.${encodeURIComponent(sf)}`);
                      }
                      // Delete old Dropbox-extracted rows for farmers we're about to import
                      // (these have source_file='APH_Database_auto' and may have different farmer_ids)
                      const farmerIds = [...new Set(allProdGroups.map(g => g.match?.farmerId).filter(Boolean))];
                      for (const fid of farmerIds) {
                        await sbDelete('production_history', `farmer_id=eq.${fid}&crop_year=eq.${cy}&source_file=eq.APH_Database_auto`);
                      }
                      // Also clean up ALL zero-production Dropbox rows for this crop year
                      // (these are farmers who haven't reported yet — they drag down county averages)
                      await sbDelete('production_history', `crop_year=eq.${cy}&source_file=eq.APH_Database_auto&production=eq.0`);
                    }
                    setXlsxStatus({ok:true, msg:`Cleared existing ${sourceFiles.join(' + ')} data for ${prodCropYears.join(',')}. Importing 0 / ${toImport.length} policies...`});
                  }

                  for (const group of toImport) {
                    try {
                      const policyId = group.match.policyId;
                      if (!policyId) { errors.push(`${group.insuredName}: no policy ID`); continue; }

                      // ── R&H PP Eligibility import path ──
                      const rhPpRows = group.rows.filter(r => r._isPP);
                      if (rhPpRows.length > 0) {

                        const ppInsertRows = rhPpRows.map(r => ({
                          policy_id: policyId,
                          county: titleCaseName(r.county || ''),
                          crop: r.crop,
                          practice: '',
                          type: '',
                          aph_yield: null,
                          pp_coverage_level: null,
                          pp_liability_per_acre: null,
                          max_acres: r.maxAcres || null,
                          planted_acres: r.remainingAcres != null ? Math.max(0, (r.maxAcres||0) - (r.remainingAcres||0)) : null,
                          yr1: null, yr1_year: null, yr2: null, yr2_year: null,
                          yr3: null, yr3_year: null, yr4: null, yr4_year: null,
                          max_acres_year: null,
                        }));
                        // Delete existing PP for this policy, preserving fall crop PP
                        const existingPp = await sbFetch('pp_eligibility', { filters: [`policy_id=eq.${policyId}`] });
                        const fallPpToKeep = existingPp.filter(p => FALL_CROPS.includes((p.crop||'').toUpperCase())).map(({id, ...rest}) => rest);
                        // Only delete non-fall PP rows
                        const hasNonFallPp = existingPp.some(p => !FALL_CROPS.includes((p.crop||'').toUpperCase()));
                        const springPP = ppInsertRows.filter(r => !FALL_CROPS.includes((r.crop||'').toUpperCase()));
                        const fallPP = ppInsertRows.filter(r => FALL_CROPS.includes((r.crop||'').toUpperCase()));
                        if (hasNonFallPp || ppInsertRows.length > 0) {
                          await sbDelete('pp_eligibility', `policy_id=eq.${policyId}`);
                          const allPpToInsert = [...fallPpToKeep, ...fallPP, ...springPP];
                          if (allPpToInsert.length > 0) {
                            try {
                              await sbInsert('pp_eligibility', allPpToInsert);
                            } catch(ppErr) {
                              if (ppErr.message?.includes('column') || ppErr.message?.includes('42703')) {
                                const ppBase = allPpToInsert.map(({aph_yield, pp_coverage_level, pp_liability_per_acre, max_acres, max_acres_year, planted_acres, ...rest}) => rest);
                                await sbInsert('pp_eligibility', ppBase);
                              } else throw ppErr;
                            }
                          }
                        }
                        totalPpRows += ppInsertRows.length;
                        done++;
                        setXlsxStatus({ok:true, msg:`Importing ${done} / ${toImport.length} policies...`});
                        continue;
                      }

                      // ── Production History import path ──
                      const prodRows = group.rows.filter(r => r._isProd);
                      if (prodRows.length > 0) {
                        const cropYear = prodRows[0]?.cropYear || CURRENT_CROP_YEAR - 1;
                        // Look up farmer_id from the policy
                        const farmerId = group.match.farmerId || null;
                        // Build production_history rows
                        const prodInsertRows = prodRows.map(r => {
                          const isSilage = (r.measure||'').toUpperCase() === 'TON';
                          const isZeroProd = !r.production || r.production === 0;
                          const isZeroAcres = !r.acres || r.acres === 0;
                          const excluded = isSilage || isZeroProd || isZeroAcres;
                          const reason = isSilage ? 'SILAGE' : isZeroProd ? 'ZERO_PRODUCTION' : isZeroAcres ? 'ZERO_ACRES' : null;
                          return {
                            farmer_id: farmerId,
                            farmer_name_raw: r.insuredName,
                            policy_number: r.policyNumber,
                            crop_year: r.cropYear || cropYear,
                            crop: r.crop,
                            county: r.county ? titleCaseName(r.county) : null,
                            state: r.state || 'NY',
                            acres: r.acres || null,
                            production: r.production || null,
                            unit_of_measure: r.measure || 'BU',
                            aip: group.aip || 'NAU',
                            excluded_from_avg: excluded,
                            exclusion_reason: reason,
                            source_file: group.aip === 'R&H' ? 'R&H 2025 Total Production Excel' : 'NAU Prior Year Net Production Excel',
                          };
                        });
                        // Bulk delete already cleared production_history for this crop year above.
                        // Just insert the new rows.
                        if (prodInsertRows.length > 0) {
                          await sbInsert('production_history', prodInsertRows);
                        }
                        totalProdRows += prodRows.length;
                        done++;
                        setXlsxStatus({ok:true, msg:`Importing ${done} / ${toImport.length} policies...`});
                        continue; // Skip crop_coverage/PP/LRP logic for production policies
                      }

                      // ── LRP import path ──
                      const lrpRows = group.rows.filter(r => r._isLRP);
                      if (lrpRows.length > 0) {
                        // Build lrp_endorsements rows
                        const lrpInsertRows = lrpRows.map(r => ({
                          policy_id: policyId,
                          sales_date: r.salesDate || null,
                          end_date: r.endDate || null,
                          weeks_out: r.weeksOut ?? null,
                          head: r.head ?? null,
                          target_weight: r.targetWeight ?? null,
                          coverage_price: r.coveragePrice ?? null,
                          insured_value: r.insuredValue ?? null,
                          premium: r.premium ?? null,
                          producer_premium: r.producerPremium ?? null,
                          subsidy: r.subsidy ?? null,
                          payable_indemnity: r.payableIndemnity ?? null,
                        }));
                        // Delete existing LRP endorsements for this policy and re-insert
                        await sbDelete('lrp_endorsements', `policy_id=eq.${policyId}`);
                        if (lrpInsertRows.length > 0) {
                          // Discover valid columns first
                          let validCols = null;
                          try {
                            const probe = await sbFetch('lrp_endorsements', { select: '*', filters: [`policy_id=eq.${policyId}`] });
                            if (probe.length > 0) validCols = new Set(Object.keys(probe[0]));
                          } catch(e) {}
                          if (!validCols) {
                            try {
                              const probe2 = await sbFetch('lrp_endorsements', { select: '*' });
                              if (probe2.length > 0) validCols = new Set(Object.keys(probe2[0]));
                            } catch(e) {}
                          }
                          const rowsToInsert = validCols
                            ? lrpInsertRows.map(row => {
                                const filtered = {};
                                Object.entries(row).forEach(([k,v]) => { if (validCols.has(k)) filtered[k] = v; });
                                return filtered;
                              })
                            : lrpInsertRows;
                          await sbInsert('lrp_endorsements', rowsToInsert);
                        }
                        totalLrpRows += lrpRows.length;
                        done++;
                        setXlsxStatus({ok:true, msg:`Importing ${done} / ${toImport.length} policies...`});
                        continue; // Skip crop_coverage/PP logic for LRP policies
                      }

                      // ── Crop coverage import path ──
                      // Separate base plans (RP/YP/APH) from SCO/ECO
                      const baseRows = group.rows.filter(r => ['RP','YP','APH'].includes(r.plan));
                      const scoEcoRows = group.rows.filter(r => (r.plan||'').includes('SCO') || (r.plan||'').includes('ECO'));

                      // Build crop_coverage rows
                      const covRows = [];
                      baseRows.forEach(r => {
                        if (FALL_CROPS_SKIP.includes(r.crop)) return;
                        covRows.push({
                          policy_id: policyId,
                          county: titleCaseName(r.county || ''),
                          crop: r.crop, plan: r.plan, practice: r.practice,
                          type: r.type, aph_yield: r.aphYield,
                          coverage_level: r.coverageLevel || null,
                          price_election: r.price || null,
                          acres: r.acres ?? null,
                          unit_count: 1,
                          total_premium: r.totalPremium ?? null,
                          subsidy: r.subsidy ?? null,
                          net_premium: r.netPremium ?? null,
                        });
                      });

                      // SCO/ECO rows
                      scoEcoRows.forEach(r => {
                        if (FALL_CROPS_SKIP.includes(r.crop)) return;
                        covRows.push({
                          policy_id: policyId,
                          county: titleCaseName(r.county || ''),
                          crop: r.crop, plan: r.plan, practice: r.practice || 'Non-Irrigated',
                          type: r.type || '', aph_yield: null,
                          coverage_level: r.coverageLevel || null,
                          price_election: null, acres: r.acres ?? null, unit_count: 1,
                          total_premium: r.totalPremium ?? null,
                          subsidy: r.subsidy ?? null,
                          net_premium: r.netPremium ?? null,
                        });
                      });

                      // Build pp_eligibility rows (NAU only — R&H doesn't have PP data in this report)
                      const ppRows = [];
                      if (group.aip === 'NAU') {
                        baseRows.forEach(r => {
                          if (FALL_CROPS_SKIP.includes(r.crop)) return;
                          if (!['RP','YP','APH'].includes(r.plan)) return; // PP for all base plans
                          ppRows.push({
                            policy_id: policyId,
                            county: titleCaseName(r.county || ''),
                            crop: r.crop, practice: r.practice || '',
                            type: r.type || '',
                            aph_yield: r.aphYield,
                            // pp_coverage_level holds the MPCI coverage level (e.g. 0.80)
                            // from the NAU "Level" column, NOT the PP coverage factor
                            // from "PP Level" (which is always 0.55 corn / 0.60 soy in
                            // 2026 post-EARP and is recomputed by getPPFactor at render
                            // time). Writing ppLevel here was the bug — caused the
                            // dashboard's Coverage column to show 55%/60% identical to
                            // the PP Factor column, and would double-count the PP
                            // factor if Priority 1 (ppLiabilityPerAcre) ever fell
                            // through to the formula.
                            pp_coverage_level: r.coverageLevel || null,
                            pp_liability_per_acre: r.ppLiabilityPerAcre || null,
                            // yr1-yr4 and max_acres come from APH PDF, not this report
                            yr1: null, yr1_year: null, yr2: null, yr2_year: null,
                            yr3: null, yr3_year: null, yr4: null, yr4_year: null,
                            max_acres: null, max_acres_year: null, planted_acres: null,
                          });
                        });
                      }

                      // Delete existing crop_coverage for this policy, preserving fall crop rows
                      // (wheat, barley, oats, etc. come from the fall crop workflow and shouldn't be touched)
                      const existingCov = await sbFetch('crop_coverage', { filters: [`policy_id=eq.${policyId}`] });
                      
                      // Strip id from preserved fall crop rows to avoid PK conflicts on re-insert
                      const fallCovToKeep = existingCov.filter(c => FALL_CROPS.includes((c.crop||'').toUpperCase())).map(({id, ...rest}) => rest);

                      await sbDelete('crop_coverage', `policy_id=eq.${policyId}`);
                      const allCovToInsert = [...fallCovToKeep, ...covRows];
                      if (allCovToInsert.length > 0) {
                        await sbInsert('crop_coverage', allCovToInsert);
                      }
                      totalCovRows += covRows.length;

                      // For PP: always clean non-wheat PP rows for matched policies.
                      // Only INSERT new PP rows if we have PP data (NAU with non-wheat crops).
                      // This ensures wheat-only policies get stale spring PP data removed.
                      {
                        const existingPp = await sbFetch('pp_eligibility', { filters: [`policy_id=eq.${policyId}`] });
                        
                        const hasNonFallPp = existingPp.some(p => !FALL_CROPS.includes((p.crop||'').toUpperCase()));
                        if (hasNonFallPp || ppRows.length > 0) {
                          // Preserve fall crop PP rows — strip id to avoid PK conflicts
                          const fallPpToKeep = existingPp.filter(p => FALL_CROPS.includes((p.crop||'').toUpperCase())).map(({id, ...rest}) => rest);
                          await sbDelete('pp_eligibility', `policy_id=eq.${policyId}`);
                          const allPpToInsert = [...fallPpToKeep, ...ppRows];
                          if (allPpToInsert.length > 0) {
                            try {
                              await sbInsert('pp_eligibility', allPpToInsert);
                            } catch(ppErr) {
                              // If extended columns don't exist, retry with base columns
                              if (ppErr.message?.includes('column') || ppErr.message?.includes('42703')) {
                                const ppBase = allPpToInsert.map(({aph_yield, pp_coverage_level, pp_liability_per_acre, ...rest}) => rest);
                                await sbInsert('pp_eligibility', ppBase);
                              } else throw ppErr;
                            }
                          }
                          totalPpRows += ppRows.length;
                        }
                      }

                      done++;
                      setXlsxStatus({ok:true, msg:`Importing ${done} / ${toImport.length} policies...`});
                    } catch(err) {
                      errors.push(`${group.insuredName} (${group.policyNumber}): ${err.message}`);
                    }
                  }

                  const summary = [`✅ Imported ${done} of ${toImport.length} policies.`,
                    `   ${totalCovRows} crop coverage rows written.`,
                    totalPpRows > 0 ? `   ${totalPpRows} PP eligibility rows written.` : '',
                    totalLrpRows > 0 ? `   ${totalLrpRows} LRP endorsement rows written.` : '',
                    totalProdRows > 0 ? `   ${totalProdRows} production history rows written (2025 yields).` : '',
                    errors.length ? `\n⚠️ ${errors.length} errors:\n${errors.slice(0,10).join('\n')}${errors.length>10?`\n...and ${errors.length-10} more`:''}` : 'No errors.'
                  ].filter(Boolean).join('\n');
                  setXlsxStatus({ok:errors.length===0, msg:summary});
                  setXlsxImporting(false);
                  if (done>0 && typeof onImport==='function') onImport();
                }} style={{padding:'11px 32px',background:xlsxImporting?'#60735f':'#2d4a2d',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:14,cursor:xlsxImporting?'not-allowed':'pointer'}}>
                  {xlsxImporting ? '⏳ Importing...' : `⬆️ Import ${xlsxPreview.filter(r=>r.status==='matched').length} Policies`}
                </button>
              </div>
            )}
          </Card>
        );
      })()}

      {activeTab === 'historical' && (
        <Card>
          <SectionTitle>HISTORICAL DATA IMPORT</SectionTitle>
          <p style={{margin:'0 0 4px',color:'#60735f',fontSize:13}}>
            Import prior-year AIP exports to build historical comparisons. Only acres and premium data are stored.
          </p>
          <p style={{margin:'0 0 14px',color:'#2d4a2d',fontSize:12,fontWeight:700}}>
            ✅ This writes to a separate historical table — it will NOT touch any current year data.
          </p>
          <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:14}}>
            <label style={{fontSize:13,fontWeight:700,color:'#1a2e1a'}}>Crop Year:</label>
            <select value={histYear} onChange={e => setHistYear(e.target.value)}
              style={{padding:'6px 12px',borderRadius:6,border:'1px solid #c8d8c8',fontSize:14,fontWeight:700}}>
              {[2020,2021,2022,2023,2024,2025].map(y => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          </div>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setHistFile(e.target.files[0]); setHistStatus(null); setHistPreview(null); setHistMapping({}); setHistFormat(null); }}
            style={{marginBottom:12,display:'block'}} />
          {histFile && <div style={{fontSize:12,color:'#4a6e4a',marginBottom:8}}>📎 {histFile.name}</div>}
          {histStatus && (
            <div style={{padding:'9px 14px',background:histStatus.ok?'#e8f5e9':'#fdecea',
              color:histStatus.ok?'#2d4a2d':'#c62828',borderRadius:6,fontSize:13,fontWeight:600,marginBottom:10,whiteSpace:'pre-wrap'}}>
              {histStatus.msg}
            </div>
          )}

          {!histPreview && (
            <button onClick={async () => {
              if (!histFile) { setHistStatus({ok:false,msg:'Please select a file.'}); return; }
              try {
                setHistStatus({ok:true,msg:'Reading file...'});
                if (!window.XLSX) {
                  await new Promise((res, rej) => { const s = document.createElement('script'); s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js'; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
                }
                const buf = await histFile.arrayBuffer();
                const wb = window.XLSX.read(buf, { cellDates: true });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = window.XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
                if (!rows.length) { setHistStatus({ok:false,msg:'No data rows found.'}); return; }
                const headers = Object.keys(rows[0]);
                const fieldDefs = [
                  { key: 'farmerName', label: 'Farmer Name', required: true, hints: ['sort name','insured name','farmer name','name','insured'] },
                  { key: 'county', label: 'County', required: true, hints: ['county','county name'] },
                  { key: 'crop', label: 'Crop', required: true, hints: ['crop','crop name'] },
                  { key: 'plan', label: 'Plan', required: false, hints: ['plan','plan name','insurance plan'] },
                  { key: 'practice', label: 'Practice', required: false, hints: ['practice','practice name'] },
                  { key: 'acres', label: 'Acres', required: false, hints: ['acres','insured acres','net acres'] },
                  { key: 'grossPremium', label: 'Gross Premium', required: false, hints: ['premium','gross premium','total premium','policy premium'] },
                  { key: 'netPremium', label: 'Net Premium', required: false, hints: ['adjusted premium','net premium','producer premium'] },
                  { key: 'policyNumber', label: 'Policy Number', required: false, hints: ['policy number','policy no','policy'] },
                  { key: 'agent', label: 'Agent', required: false, hints: ['agent','agent of record'] },
                ];

                // Detect NAU format: has 'Insured' + 'Total Premium' + numeric Plan codes
                const hdrSet = new Set(headers.map(h => h.toLowerCase().trim()));
                const isNAU = hdrSet.has('insured') && hdrSet.has('total premium') && hdrSet.has('plan') && hdrSet.has('acres');
                const isRH = hdrSet.has('max eligible acres') || hdrSet.has('sort name') || (hdrSet.has('name') && hdrSet.has('max eligible acres'));
                const detectedFormat = isNAU ? 'NAU' : (isRH ? 'RH' : null);
                setHistFormat(detectedFormat);

                const autoMap = {};
                if (isNAU) {
                  // Hard-wire NAU column mapping — no guessing needed
                  autoMap.farmerName = 'Insured';
                  autoMap.county = 'County';
                  autoMap.crop = 'Crop';
                  autoMap.plan = 'Plan';
                  autoMap.practice = 'Practice';
                  autoMap.acres = 'Acres';
                  autoMap.grossPremium = 'Total Premium';
                  autoMap.netPremium = 'Net Premium';
                  autoMap.policyNumber = 'Policy';
                  autoMap.agent = 'Agent';
                } else {
                  // Generic auto-detect for R&H and other formats
                  fieldDefs.forEach(fd => {
                    const lh = headers.map(h => h.toLowerCase().trim());
                    for (const hint of fd.hints) { const idx = lh.indexOf(hint); if (idx >= 0) { autoMap[fd.key] = headers[idx]; break; } }
                  });
                }
                setHistPreview({ rows, headers, fieldDefs });
                setHistMapping(autoMap);
                const nauNote = isNAU ? ' NAU format detected — columns pre-mapped.' : '';
                setHistStatus({ok:true,msg:`Found ${rows.length} rows.${nauNote} Confirm mapping, then import as ${histYear} historical data.`});
              } catch(err) { setHistStatus({ok:false,msg:'Error: ' + err.message}); }
            }} style={{padding:'10px 28px',background:'#2d4a2d',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:14,cursor:'pointer'}}>
              📖 Read File
            </button>
          )}

          {histPreview && (
            <div>
              {histFormat === 'NAU' && (
                <div style={{padding:'8px 14px',background:'#e8f0fb',color:'#1a3a6e',borderRadius:6,fontSize:12,fontWeight:700,marginBottom:12,border:'1px solid #b0c4e8'}}>
                  🏢 NAU format detected — columns pre-mapped. All agency rows imported (both agents). Plan codes translated automatically (02→RP, 88→SCO-RP, 87→ECO-RP, 13→PRF).
                </div>
              )}
              {histFormat === 'RH' && (
                <div style={{padding:'8px 14px',background:'#e8f5e9',color:'#1a4a2d',borderRadius:6,fontSize:12,fontWeight:700,marginBottom:12,border:'1px solid #a5d6a7'}}>
                  🌧 Rain &amp; Hail format detected — columns pre-mapped.
                </div>
              )}
              <div style={{fontWeight:700,fontSize:14,color:'#1a2e1a',marginBottom:10}}>Column Mapping</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 16px',marginBottom:16}}>
                {histPreview.fieldDefs.map(fd => (
                  <div key={fd.key} style={{display:'flex',alignItems:'center',gap:8}}>
                    <label style={{fontSize:12,fontWeight:700,color:'#4a6e4a',minWidth:120,textAlign:'right'}}>
                      {fd.label}{fd.required ? ' *' : ''}
                    </label>
                    <select value={histMapping[fd.key]||''} onChange={e => setHistMapping(prev => ({...prev,[fd.key]:e.target.value}))}
                      style={{flex:1,padding:'4px 8px',borderRadius:5,border:'1px solid #c8d8c8',fontSize:12,background:histMapping[fd.key]?'#e8f5e9':'#fff'}}>
                      <option value="">— not mapped —</option>
                      {histPreview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:10}}>
                <button onClick={async () => {
                  const map = histMapping;
                  if (!map.farmerName || !map.county || !map.crop) {
                    setHistStatus({ok:false,msg:'Farmer Name, County, and Crop are required.'}); return;
                  }
                  try {
                    setHistStatus({ok:true,msg:`Importing ${histYear} historical data...`});
                    const normNum = v => { if (!v && v !== 0) return null; const n = parseFloat(String(v).replace(/[$,]/g,'').trim()); return isNaN(n) ? null : n; };
                    const normP = p => { if (!p) return 'Non-Irrigated'; const u = String(p).toUpperCase().trim(); if (u.includes('ORGANIC')) return 'Organic'; if (u.includes('NON') && (u.includes('IRR')||u.includes('IRRIGAT'))) return 'Non-Irrigated'; if (!u) return 'Non-Irrigated'; return p; };
                    // NAU numeric plan codes: 02=RP, 01=YP, 88=SCO-RP, 87=ECO-RP, 13=PRF, 31/32/90=RP
                    const NAU_PLAN_CODES = {'02':'RP','01':'YP','88':'SCO-RP','87':'ECO-RP','13':'PRF','31':'RP','32':'RP','90':'RP'};
                    const normPlan = p => {
                      const raw = String(p||'').trim();
                      if (NAU_PLAN_CODES[raw]) return NAU_PLAN_CODES[raw];
                      const u = raw.toUpperCase();
                      if (u.includes('SUPP COV')||u.includes('SCO')) return 'SCO-RP';
                      if (u.includes('ENHANCED COV')||u.includes('ECO')) return 'ECO-RP';
                      if (u.includes('REVENUE')) return 'RP';
                      if (u.includes('YIELD')||u==='YP') return 'YP';
                      if (u==='APH') return 'APH';
                      if (u.includes('RAINFALL')) return 'PRF';
                      if (u.includes('LIVESTOCK')) return 'LRP';
                      if (u.includes('DAIRY')) return 'DRP';
                      return raw||'RP';
                    };
                    const normFarmerName = n => { const s = String(n||'').trim(); if (s.includes(',')) { const pts = s.split(',').map(x=>x.trim()); return pts[1]+' '+pts[0]; } return s; };
                    const normCrop = c => {
                      const s = String(c||'').trim().toUpperCase();
                      if (s === 'PRF' || s === 'PASTURE' || s.includes('PASTURE,RANGE')) return 'PASTURE,RANGELAND,FORAGE';
                      return s;
                    };
                    const cropYear = parseInt(histYear);
                    const isNAUImport = histFormat === 'NAU';

                    // Group by farmer+county+crop+practice+plan and sum acres/premium
                    const groups = {};
                    histPreview.rows.forEach(row => {
                      const rawName = String(row[map.farmerName]||'').trim();
                      if (!rawName) return;
                      const name = normFarmerName(rawName);
                      const county = String(row[map.county]||'').toUpperCase().trim();
                      const crop = normCrop(String(row[map.crop]||'').trim());
                      const practice = normP(map.practice ? row[map.practice] : null);
                      const plan = normPlan(map.plan ? row[map.plan] : '');
                      const acres = map.acres ? normNum(row[map.acres]) : null;
                      const gross = map.grossPremium ? normNum(row[map.grossPremium]) : null;
                      const net = map.netPremium ? normNum(row[map.netPremium]) : null;

                      // Skip zero-acre, zero-premium rows
                      if ((!acres || acres === 0) && (!gross || gross === 0)) return;

                      // Keep RP, SCO-RP, ECO-RP separate for premium tracking
                      const key = `${name}|${county}|${crop}|${practice}|${plan}`;
                      if (!groups[key]) groups[key] = {
                        farmer_name: name,
                        policy_number: map.policyNumber ? String(row[map.policyNumber]||'').trim() : null,
                        aip: null,
                        agent: map.agent ? String(row[map.agent]||'').trim() : null,
                        county, crop, practice,
                        type: plan,
                        acres: 0, gross_premium: 0, net_premium: 0, subsidy: 0,
                      };
                      if (acres) groups[key].acres += acres;
                      if (gross) groups[key].gross_premium += gross;
                      if (net) groups[key].net_premium += net;
                      if (gross && net) groups[key].subsidy += (gross - net);
                      if (map.policyNumber && row[map.policyNumber]) {
                        groups[key].policy_number = String(row[map.policyNumber]).trim();
                        // Detect AIP from policy number or format flag
                        const pn = groups[key].policy_number;
                        if (isNAUImport) groups[key].aip = 'NAU';
                        else if (pn.startsWith('MP-')) groups[key].aip = 'RAIN_AND_HAIL';
                        else if (pn.includes('-944-') || pn.includes('-942-')) groups[key].aip = 'NAU';
                      } else if (isNAUImport) {
                        groups[key].aip = 'NAU';
                      }
                    });

                    const summaryRows = Object.values(groups).map(g => ({
                      crop_year: cropYear,
                      farmer_name: g.farmer_name,
                      policy_number: g.policy_number,
                      aip: g.aip,
                      agent: g.agent,
                      agency: null,
                      county: g.county,
                      crop: g.crop,
                      type: g.type,
                      practice: g.practice,
                      acres: Math.round(g.acres * 100) / 100,
                      gross_premium: Math.round(g.gross_premium * 100) / 100,
                      subsidy: Math.round(g.subsidy * 100) / 100,
                      net_premium: Math.round(g.net_premium * 100) / 100,
                    }));

                    // Delete existing historical data for this crop year (scoped to AIP for NAU imports)
                    if (isNAUImport) {
                      await sbDelete("historical_summaries", `crop_year=eq.${cropYear}&aip=eq.NAU`);
                    } else {
                      await sbDelete("historical_summaries", `crop_year=eq.${cropYear}`);
                    }

                    // Insert in batches of 50
                    let inserted = 0;
                    for (let i = 0; i < summaryRows.length; i += 50) {
                      const batch = summaryRows.slice(i, i + 50);
                      await sbInsert("historical_summaries", batch);
                      inserted += batch.length;
                    }

                    const totalAcres = summaryRows.reduce((s,r) => s + (r.acres||0), 0);
                    const totalPrem = summaryRows.reduce((s,r) => s + (r.gross_premium||0), 0);
                    const farmerCount = new Set(summaryRows.map(r => r.farmer_name)).size;
                    setHistStatus({ok:true,msg:`✅ Imported ${inserted} historical summary rows for ${cropYear}\n${farmerCount} farmers · ${totalAcres.toLocaleString()} total acres · $${totalPrem.toLocaleString()} gross premium`});
                    setHistPreview(null);
                  } catch(err) { setHistStatus({ok:false,msg:'Import error: ' + err.message}); }
                }} style={{padding:'10px 28px',background:'#6a4a1a',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:14,cursor:'pointer'}}>
                  📜 Import as {histYear} Historical Data
                </button>
                <button onClick={() => { setHistPreview(null); setHistMapping({}); setHistStatus(null); }}
                  style={{padding:'10px 20px',background:'#e4ece4',color:'#2d4a2d',border:'none',borderRadius:7,fontWeight:600,fontSize:13,cursor:'pointer'}}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </Card>
      )}


      {activeTab === 'rma' && (
        <Card>
          <SectionTitle>RMA MARKET DATA IMPORT</SectionTitle>
          <p style={{margin:'0 0 4px',color:'#60735f',fontSize:13}}>
            Import USDA RMA Summary of Business data to enable market share analysis on the historical dashboard.
          </p>
          <p style={{margin:'0 0 14px',color:'#2d4a2d',fontSize:12,fontWeight:700}}>
            ✅ Writes only to rma_county_data — never touches farmer or policy data.
          </p>
          <p style={{margin:'0 0 14px',color:'#60735f',fontSize:12}}>
            Download from RMA Summary of Business → filter by State, Crop Year, and desired counties/crops → export as Excel.
          </p>
          <input type="file" accept=".xlsx,.xls,.csv"
            onChange={e => { setRmaFile(e.target.files[0]); setRmaStatus(null); }}
            style={{marginBottom:12,display:'block'}} />
          {rmaFile && <div style={{fontSize:12,color:'#4a6e4a',marginBottom:8}}>📎 {rmaFile.name}</div>}
          {rmaStatus && (
            <div style={{padding:'9px 14px',background:rmaStatus.ok?'#e8f5e9':'#fdecea',
              color:rmaStatus.ok?'#2d4a2d':'#c62828',borderRadius:6,fontSize:13,fontWeight:600,marginBottom:10,whiteSpace:'pre-wrap'}}>
              {rmaStatus.msg}
            </div>
          )}
          <button onClick={async () => {
            if (!rmaFile) { setRmaStatus({ok:false,msg:'Please select a file.'}); return; }
            try {
              setRmaStatus({ok:true,msg:'Reading file...'});
              if (!window.XLSX) {
                await new Promise((res,rej) => { const s=document.createElement('script'); s.src='https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js'; s.onload=res; s.onerror=rej; document.head.appendChild(s); });
              }
              const buf = await rmaFile.arrayBuffer();
              const wb = window.XLSX.read(buf, {cellDates:true});
              const ws = wb.Sheets[wb.SheetNames[0]];
              const raw = window.XLSX.utils.sheet_to_json(ws, {defval:'', raw:true});
              if (!raw.length) { setRmaStatus({ok:false,msg:'No data rows found.'}); return; }

              // Detect format: RMA Summary of Business has specific column names
              const firstRow = raw[0];
              const keys = Object.keys(firstRow);
              const hasCommodityYear = keys.some(k => k.toLowerCase().includes('commodity year'));
              const hasCommodityName = keys.some(k => k.toLowerCase().includes('commodity name'));
              if (!hasCommodityYear || !hasCommodityName) {
                setRmaStatus({ok:false,msg:'File does not look like an RMA Summary of Business export. Expected columns: "Commodity Year", "Commodity Name", "County Name", etc.'}); return;
              }

              // Find column names (case-insensitive)
              const findCol = (hint) => keys.find(k => k.toLowerCase().includes(hint.toLowerCase())) || null;
              const colYear    = findCol('commodity year');
              const colCrop    = findCol('commodity name');
              const colState   = findCol('state abbrv');
              const colCounty  = findCol('county name');
              const colPolicies= findCol('policies earning prem');
              const colAcres   = findCol('quantity');
              const colPremium = findCol('total prem');

              const normRmaCrop = c => {
                const u = String(c||'').trim().toUpperCase();
                if (u === 'PASTURE,RANGELAND,FORAGE' || u === 'PRF') return 'PASTURE,RANGELAND,FORAGE';
                return u;
              };

              // Build rows — skip zero-policy rows
              const rows = [];
              const yearsSeen = new Set();
              const statesSeen = new Set();
              raw.forEach(r => {
                const policies = parseInt(r[colPolicies]||0);
                if (policies <= 0) return;
                const cropYear = parseInt(r[colYear]||0);
                const state = String(r[colState]||'').trim().toUpperCase();
                const county = String(r[colCounty]||'').trim();
                const crop = normRmaCrop(r[colCrop]);
                const acres = parseFloat(r[colAcres]||0);
                const premium = parseFloat(r[colPremium]||0);
                if (!cropYear || !county || !crop) return;
                yearsSeen.add(cropYear);
                statesSeen.add(state);
                rows.push({ crop_year:cropYear, state, county, crop, rma_policies:policies, rma_acres:acres, rma_premium:premium });
              });

              if (!rows.length) { setRmaStatus({ok:false,msg:'No valid rows found (all had zero policies earning premium).'}); return; }

              // Delete existing data for each crop_year+state combination found
              const combos = [...new Set(rows.map(r => `${r.crop_year}|${r.state}`))];
              setRmaStatus({ok:true,msg:`Importing ${rows.length} rows for ${[...yearsSeen].join(', ')} (${[...statesSeen].join(', ')})...`});
              for (const combo of combos) {
                const [yr, st] = combo.split('|');
                await sbDelete('rma_county_data', `crop_year=eq.${yr}&state=eq.${st}`);
              }
              for (let i=0; i<rows.length; i+=50) {
                await sbInsert('rma_county_data', rows.slice(i, i+50));
              }

              const crops = [...new Set(rows.map(r => r.crop === 'PASTURE,RANGELAND,FORAGE' ? 'PRF' : r.crop))];
              const counties = [...new Set(rows.map(r => r.county))];
              setRmaStatus({ok:true,msg:`✅ Imported ${rows.length} RMA rows\n${[...yearsSeen].join(', ')} · ${[...statesSeen].join(', ')} · ${counties.length} counties · ${crops.length} crops\nCrops: ${crops.join(', ')}\nCounties: ${counties.join(', ')}`});
            } catch(err) { setRmaStatus({ok:false,msg:'Import error: '+err.message}); }
          }} style={{padding:'10px 28px',background:'#1a3a6e',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:14,cursor:'pointer'}}>
            📊 Import RMA Data
          </button>
        </Card>
      )}


      {activeTab === 'audit' && (
        <Card>
          <SectionTitle>AUDIT / COMPARE (READ-ONLY)</SectionTitle>
          <p style={{margin:'0 0 4px',color:'#60735f',fontSize:13}}>
            Upload an AIP export (Rain & Hail, NAU, AgencyRoot) and compare it against current app data.
          </p>
          <p style={{margin:'0 0 14px',color:'#c62828',fontSize:12,fontWeight:700}}>
            ⚠️ This tool is strictly read-only — it will NOT modify any data in the app.
          </p>
          <div style={{display:'flex',gap:6,marginBottom:14}}>
            {[{key:'crop',label:'🌾 Crop Coverage (yields, prices, acres)'},{key:'pp',label:'📊 PP Eligibility (planted acres history)'}].map(t => (
              <button key={t.key} onClick={() => { setAuditType(t.key); setAuditPreview(null); setAuditMapping({}); setAuditReport(null); setAuditStatus(null); setAuditFile(null); }}
                style={{padding:'7px 14px',borderRadius:6,border: auditType===t.key ? '2px solid #2d4a2d' : '1px solid #c8d8c8',
                  background: auditType===t.key ? '#e8f5e9' : '#fff', color: auditType===t.key ? '#1a2e1a' : '#60735f',
                  fontWeight: auditType===t.key ? 700 : 500, fontSize:12, cursor:'pointer'}}>{t.label}</button>
            ))}
          </div>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setAuditFile(e.target.files[0]); setAuditStatus(null); setAuditPreview(null); setAuditMapping({}); setAuditReport(null); }}
            style={{marginBottom:12,display:'block'}} />
          {auditFile && <div style={{fontSize:12,color:'#4a6e4a',marginBottom:8}}>📎 {auditFile.name}</div>}
          {auditStatus && (
            <div style={{padding:'9px 14px',background:auditStatus.ok?'#e8f5e9':'#fdecea',
              color:auditStatus.ok?'#2d4a2d':'#c62828',borderRadius:6,fontSize:13,fontWeight:600,marginBottom:10, whiteSpace:'pre-wrap'}}>
              {auditStatus.msg}
            </div>
          )}

          {/* Step 1: Parse & Preview */}
          {!auditPreview && !auditReport && (
            <button onClick={async () => {
              if (!auditFile) { setAuditStatus({ok:false,msg:'Please select a file first.'}); return; }
              try {
                setAuditStatus({ok:true,msg:'Reading file...'});
                if (!window.XLSX) {
                  await new Promise((res, rej) => {
                    const s = document.createElement('script');
                    s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
                    s.onload = res; s.onerror = rej;
                    document.head.appendChild(s);
                  });
                }
                const XLSX = window.XLSX;
                const buf = await auditFile.arrayBuffer();
                const wb = XLSX.read(buf, { cellDates: true });
                const ws = wb.Sheets[wb.SheetNames[0]];
                let rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
                if (!rows.length) { setAuditStatus({ok:false,msg:'No data rows found.'}); return; }
                let headers = Object.keys(rows[0]);

                // Auto-detect if headers are in row 2 (e.g., R&H PP report has title in row 1)
                // Check if most header names are blank/None or if the first data row looks like headers
                const blankHeaders = headers.filter(h => !h || h === 'None' || h.startsWith('__EMPTY')).length;
                if (blankHeaders > headers.length / 2 && rows.length > 0) {
                  // Row 1 is likely a title — row 2 has the real headers
                  const realHeaders = Object.values(rows[0]).map(v => String(v||'').trim());
                  if (realHeaders.some(h => /name|county|crop|acres|policy/i.test(h))) {
                    // Re-parse with header row = 2
                    const rows2 = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true, range: 1 });
                    if (rows2.length) {
                      rows = rows2;
                      headers = Object.keys(rows[0]);
                    }
                  }
                }

                const cropFieldDefs = [
                  { key: 'farmerName', label: 'Farmer Name', required: true, hints: ['sort name','insured name','farmer name','policyholder','name','insured'] },
                  { key: 'policyNumber', label: 'Policy Number', required: false, hints: ['policy number','policy no','policy #','policy_number','contract','policy'] },
                  { key: 'county', label: 'County', required: true, hints: ['county','county name'] },
                  { key: 'crop', label: 'Crop', required: true, hints: ['crop','crop name','commodity'] },
                  { key: 'plan', label: 'Plan', required: false, hints: ['plan','plan name','coverage type','insurance plan'] },
                  { key: 'practice', label: 'Practice', required: false, hints: ['practice','practice name','practice type'] },
                  { key: 'approvedYield', label: 'Approved Yield', required: false, hints: ['approved yield','appr yield','aph yield','yield','appr yld'] },
                  { key: 'coverageLevel', label: 'Coverage Level', required: false, hints: ['level','coverage level','cov level','coverage %'] },
                  { key: 'priceElection', label: 'Price Election', required: false, hints: ['price','price election','projected price'] },
                  { key: 'acres', label: 'Acres', required: false, hints: ['acres','insured acres','net acres','planted acres','total acres'] },
                  { key: 'grossPremium', label: 'Gross Premium', required: false, hints: ['policy premium','gross premium','total premium','premium'] },
                ];
                const ppBaseFieldDefs = [
                  { key: 'farmerName', label: 'Farmer Name', required: true, hints: ['sort name','insured name','farmer name','policyholder','name'] },
                  { key: 'county', label: 'County', required: true, hints: ['county','county name'] },
                  { key: 'crop', label: 'Crop', required: true, hints: ['crop','crop name','commodity'] },
                  { key: 'practice', label: 'Practice', required: false, hints: ['practice','practice name','practice type'] },
                  { key: 'type', label: 'Type', required: false, hints: ['type','type name'] },
                  { key: 'maxEligible', label: 'Max Eligible Acres', required: false, hints: ['max eligible acres','max eligible','max acres','max elig acres'] },
                ];
                // Auto-detect year columns: match headers like "2025 Acres", "2024 Acres", etc.
                const yearCols = headers
                  .filter(h => /^\d{4}\s+acres$/i.test(h.trim()))
                  .map(h => ({ header: h, year: parseInt(h.trim().match(/^(\d{4})/)[1]) }))
                  .sort((a, b) => b.year - a.year); // newest first

                const ppYearFieldDefs = yearCols.map((yc, i) => ({
                  key: `yr${i + 1}`,
                  label: `${yc.year} Acres${i === 0 ? ' (most recent)' : i === yearCols.length - 1 ? ' (oldest)' : ''}`,
                  required: false,
                  hints: [yc.header.toLowerCase()],
                  year: yc.year,
                }));

                const ppFieldDefs = [...ppBaseFieldDefs, ...ppYearFieldDefs];
                const fieldDefs = auditType === 'pp' ? ppFieldDefs : cropFieldDefs;
                const autoMap = {};
                fieldDefs.forEach(fd => {
                  const lh = headers.map(h => h.toLowerCase().trim());
                  for (const hint of fd.hints) { const idx = lh.indexOf(hint); if (idx >= 0) { autoMap[fd.key] = headers[idx]; break; } }
                });

                // Detect unique crops in the file for filtering
                const cropCol = autoMap.crop;
                const uniqueCrops = cropCol
                  ? [...new Set(rows.map(r => String(r[cropCol]||'').toUpperCase().trim()).filter(Boolean))].sort()
                  : [];
                const defaultFilter = {};
                uniqueCrops.forEach(c => { defaultFilter[c] = true; }); // all on by default

                setAuditPreview({ rows, headers, fieldDefs, uniqueCrops });
                setAuditMapping(autoMap);
                setAuditCropFilter(defaultFilter);
                setAuditStatus({ok:true,msg:`Found ${rows.length} rows, ${headers.length} columns, ${uniqueCrops.length} crops. Select crops to audit, confirm mapping, then run.`});
              } catch(err) { setAuditStatus({ok:false,msg:'Error: ' + err.message}); }
            }} style={{padding:'10px 28px',background:'#2d4a2d',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:14,cursor:'pointer'}}>
              📖 Read File
            </button>
          )}

          {/* Step 2: Column Mapping + Crop Filter */}
          {auditPreview && !auditReport && (
            <div>
              <div style={{fontWeight:700,fontSize:14,color:'#1a2e1a',marginBottom:10}}>Column Mapping</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 16px',marginBottom:16}}>
                {auditPreview.fieldDefs.map(fd => (
                  <div key={fd.key} style={{display:'flex',alignItems:'center',gap:8}}>
                    <label style={{fontSize:12,fontWeight:700,color:'#4a6e4a',minWidth:120,textAlign:'right'}}>
                      {fd.label}{fd.required ? ' *' : ''}
                    </label>
                    <select value={auditMapping[fd.key]||''} onChange={e => setAuditMapping(prev => ({...prev, [fd.key]: e.target.value}))}
                      style={{flex:1,padding:'4px 8px',borderRadius:5,border:'1px solid #c8d8c8',fontSize:12,background:auditMapping[fd.key]?'#e8f5e9':'#fff'}}>
                      <option value="">— not mapped —</option>
                      {auditPreview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              {/* Crop Filter */}
              {auditPreview.uniqueCrops && auditPreview.uniqueCrops.length > 0 && (
                <div style={{marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:14,color:'#1a2e1a',marginBottom:8}}>Crop Filter — select which crops to audit</div>
                  <div style={{display:'flex',gap:6,marginBottom:8,flexWrap:'wrap'}}>
                    {(() => {
                      const SPRING = ['CORN','SOYBEANS','DRY BEANS','PROCESSING BEANS','POTATOES','BARLEY','OATS','GRAIN SORGHUM'];
                      const FALL = ['WHEAT','TRITICALE'];
                      return (<>
                        <button onClick={() => { const f = {}; auditPreview.uniqueCrops.forEach(c => { f[c] = SPRING.includes(c); }); setAuditCropFilter(f); }}
                          style={{padding:'5px 12px',borderRadius:5,border:'1px solid #4a7a2a',background:'#e8f5e9',color:'#2d4a2d',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          🌽 Spring Crops Only
                        </button>
                        <button onClick={() => { const f = {}; auditPreview.uniqueCrops.forEach(c => { f[c] = FALL.includes(c); }); setAuditCropFilter(f); }}
                          style={{padding:'5px 12px',borderRadius:5,border:'1px solid #c8a000',background:'#fff8e0',color:'#6a5000',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          🌾 Fall Crops Only
                        </button>
                        <button onClick={() => { const f = {}; auditPreview.uniqueCrops.forEach(c => { f[c] = true; }); setAuditCropFilter(f); }}
                          style={{padding:'5px 12px',borderRadius:5,border:'1px solid #c8d8c8',background:'#fff',color:'#4a6e4a',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                          All Crops
                        </button>
                        <button onClick={() => { const f = {}; auditPreview.uniqueCrops.forEach(c => { f[c] = false; }); setAuditCropFilter(f); }}
                          style={{padding:'5px 12px',borderRadius:5,border:'1px solid #c8d8c8',background:'#fff',color:'#999',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                          None
                        </button>
                      </>);
                    })()}
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    {auditPreview.uniqueCrops.map(crop => (
                      <label key={crop} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,cursor:'pointer',
                        padding:'4px 10px',borderRadius:5,border: auditCropFilter[crop] ? '1px solid #4a7a2a' : '1px solid #ddd',
                        background: auditCropFilter[crop] ? '#e8f5e9' : '#f8f8f8',color: auditCropFilter[crop] ? '#1a2e1a' : '#999'}}>
                        <input type="checkbox" checked={!!auditCropFilter[crop]}
                          onChange={e => setAuditCropFilter(prev => ({...prev, [crop]: e.target.checked}))}
                          style={{margin:0}} />
                        {crop.charAt(0) + crop.slice(1).toLowerCase()}
                      </label>
                    ))}
                  </div>
                  <div style={{fontSize:11,color:'#60735f',marginTop:4}}>
                    {Object.values(auditCropFilter).filter(Boolean).length} of {auditPreview.uniqueCrops.length} crops selected
                  </div>
                </div>
              )}
              <div style={{display:'flex',gap:10}}>
                <button onClick={async () => {
                  const map = auditMapping;
                  if (!map.farmerName || !map.county || !map.crop) {
                    setAuditStatus({ok:false,msg:'Farmer Name, County, and Crop are required.'}); return;
                  }
                  try {
                    setAuditStatus({ok:true,msg:'Running audit (read-only)...'});
                    // Filter rows by selected crops
                    const selectedCrops = new Set(Object.entries(auditCropFilter).filter(([,v]) => v).map(([k]) => k));
                    const cropCol = map.crop;
                    const allRows = auditPreview.rows;
                    const rows = cropCol && selectedCrops.size > 0
                      ? allRows.filter(r => selectedCrops.has(String(r[cropCol]||'').toUpperCase().trim()))
                      : allRows;
                    if (!rows.length) { setAuditStatus({ok:false,msg:'No rows match the selected crop filter.'}); return; }
                    const normP = p => { if (!p) return 'Non-Irrigated'; const u = String(p).toUpperCase().trim(); if (u.includes('ORGANIC')) return 'Organic'; if (u.includes('NON') && (u.includes('IRR')||u.includes('IRRIGAT'))) return 'Non-Irrigated'; if (u === 'NO PRACTICE SPECIFIED' || u === 'NFAC (NON-IRRIGATED)') return 'Non-Irrigated'; return p; };
                    const normNum = v => { if (!v && v !== 0) return null; const n = parseFloat(String(v).replace(/[$,%]/g,'').trim()); return isNaN(n) ? null : n; };
                    const normCov = v => { const n = normNum(v); if (n == null) return null; return n > 1 ? n/100 : n; };
                    const normPlan = p => { const u = (p||'').toUpperCase(); if (u.includes('SUPP COV')||u.includes('SCO')) return 'SCO-RP'; if (u.includes('ENHANCED COV')||u.includes('ECO')) return 'ECO-RP'; return 'RP'; };
                    const normFarmerName = n => { const s = String(n||'').trim(); if (s.includes(',')) { const p = s.split(',').map(x=>x.trim()); return (p[1]+' '+p[0]).toUpperCase(); } return s.toUpperCase(); };

                    // Fetch app data (read-only)
                    const [allFarmerRows, allPolicies, allCoverage, allPP] = await Promise.all([
                      sbFetch("farmers").catch(() => []),
                      sbFetch("policies").catch(() => []),
                      sbFetch("crop_coverage").catch(() => []),
                      sbFetch("pp_eligibility").catch(() => []),
                    ]);

                    if (auditType === 'pp') {
                      // ═══ PP ELIGIBILITY AUDIT ═══
                      // Detect which year columns were mapped
                      const mappedYears = auditPreview.fieldDefs
                        .filter(fd => fd.key.startsWith('yr') && fd.year && map[fd.key])
                        .map(fd => ({ key: fd.key, year: fd.year, header: map[fd.key] }))
                        .sort((a, b) => b.year - a.year); // newest first

                      // Group file rows by farmer
                      const fileByFarmer = {};
                      rows.forEach(row => {
                        const rawName = String(row[map.farmerName]||'').trim();
                        if (!rawName) return;
                        const normName = normFarmerName(rawName);
                        if (!fileByFarmer[normName]) fileByFarmer[normName] = { rawName, rows: [] };
                        const yearData = {};
                        mappedYears.forEach(my => {
                          yearData[my.year] = normNum(row[my.header]);
                        });
                        const fileMaxElig = map.maxEligible ? normNum(row[map.maxEligible]) : null;
                        fileByFarmer[normName].rows.push({
                          county: String(row[map.county]||'').toUpperCase().trim(),
                          crop: String(row[map.crop]||'').toUpperCase().trim(),
                          practice: normP(map.practice ? row[map.practice] : null),
                          type: map.type ? String(row[map.type]||'').toUpperCase().trim() : '',
                          yearData,
                          fileMaxEligible: fileMaxElig,
                        });
                      });

                      // Build app PP data lookup by farmer name — store with year keys
                      const appByFarmer = {};
                      allPolicies.forEach(pol => {
                        const f = allFarmerRows.find(fr => fr.id === pol.farmer_id);
                        if (!f) return;
                        const normName = f.name.trim().toUpperCase();
                        if (!appByFarmer[normName]) appByFarmer[normName] = { name: f.name, ppRows: [] };
                        const pps = allPP.filter(p => p.policy_id === pol.id);
                        pps.forEach(p => {
                          const yearData = {};
                          if (p.yr1_year && p.yr1 != null) yearData[p.yr1_year] = p.yr1;
                          if (p.yr2_year && p.yr2 != null) yearData[p.yr2_year] = p.yr2;
                          if (p.yr3_year && p.yr3 != null) yearData[p.yr3_year] = p.yr3;
                          if (p.yr4_year && p.yr4 != null) yearData[p.yr4_year] = p.yr4;
                          // Fallback: if no year labels stored, assume yr1=most recent year in file, etc.
                          if (!p.yr1_year && mappedYears.length >= 1 && p.yr1 != null) yearData[mappedYears[0]?.year] = p.yr1;
                          if (!p.yr2_year && mappedYears.length >= 2 && p.yr2 != null) yearData[mappedYears[1]?.year] = p.yr2;
                          if (!p.yr3_year && mappedYears.length >= 3 && p.yr3 != null) yearData[mappedYears[2]?.year] = p.yr3;
                          if (!p.yr4_year && mappedYears.length >= 4 && p.yr4 != null) yearData[mappedYears[3]?.year] = p.yr4;
                          // Dedup: skip if this farmer already has a PP row for same county+crop+practice
                          const county = (p.county||'').toUpperCase();
                          const crop = (p.crop||'').toUpperCase();
                          const practice = normP(p.practice);
                          const type = (p.type||'').toUpperCase();
                          const dupeKey = `${county}|${crop}|${practice}|${type}`;
                          const existing = appByFarmer[normName].ppRows.find(r => `${r.county}|${r.crop}|${r.practice}|${r.type}` === dupeKey);
                          if (existing) {
                            // Merge: prefer the one with more year data
                            const existingYears = Object.keys(existing.yearData).length;
                            const newYears = Object.keys(yearData).length;
                            if (newYears > existingYears) {
                              existing.yearData = yearData;
                              existing.maxAcres = p.max_acres;
                            }
                            return; // skip adding duplicate
                          }
                          appByFarmer[normName].ppRows.push({
                            county, crop, practice,
                            type: (p.type||'').toUpperCase(),
                            yearData,
                            maxAcres: p.max_acres,
                          });
                        });
                      });

                      // Compare
                      const report = { matches: [], mismatches: [], notInApp: [], auditType: 'pp' };
                      Object.entries(fileByFarmer).forEach(([normName, fileData]) => {
                        const appData = appByFarmer[normName];
                        if (!appData) {
                          report.notInApp.push({ name: fileData.rawName, rows: fileData.rows });
                          return;
                        }
                        const farmerReport = { name: appData.name, mismatches: [], inFileOnly: [], inAppOnly: [], matches: [] };
                        const appUsed = new Set();

                        fileData.rows.forEach(fr => {
                          // When file has no type, merge all app rows for same county+crop+practice
                          const hasType = fr.type && fr.type.length > 0;
                          const matchingAppRows = appData.ppRows
                            .map((ar, idx) => ({ ar, idx }))
                            .filter(({ ar, idx }) => {
                              if (appUsed.has(idx)) return false;
                              const countyMatch = ar.county === fr.county || normalizeCounty(ar.county) === normalizeCounty(fr.county);
                              const cropMatch = ar.crop === fr.crop;
                              const practiceMatch = ar.practice === fr.practice;
                              const typeMatch = hasType ? (!ar.type || ar.type === fr.type) : true;
                              return countyMatch && cropMatch && practiceMatch && typeMatch;
                            });

                          if (!matchingAppRows.length) { farmerReport.inFileOnly.push(fr); return; }

                          // Mark all matching rows as used
                          matchingAppRows.forEach(({ idx }) => appUsed.add(idx));

                          // Merge app rows: combine yearData, sum maxAcres
                          const mergedYearData = {};
                          let mergedMaxAcres = 0;
                          matchingAppRows.forEach(({ ar }) => {
                            Object.entries(ar.yearData || {}).forEach(([yr, val]) => {
                              mergedYearData[yr] = (mergedYearData[yr] || 0) + (val || 0);
                            });
                            mergedMaxAcres += ar.maxAcres || 0;
                          });
                          const appMatch = { ...matchingAppRows[0].ar, yearData: mergedYearData, maxAcres: mergedMaxAcres };

                          const diffs = [];
                          // Compare each year from the file against app data by actual year
                          mappedYears.forEach(my => {
                            const fileVal = fr.yearData[my.year];
                            const appVal = appMatch.yearData[my.year];
                            if (fileVal != null && appVal != null && Math.abs(fileVal - appVal) > 0.5) {
                              diffs.push({ field: `${my.year} Acres`, file: fileVal, app: appVal });
                            } else if (fileVal != null && fileVal > 0 && (appVal == null || appVal === 0)) {
                              diffs.push({ field: `${my.year} Acres`, file: fileVal, app: appVal || 0 });
                            } else if ((fileVal == null || fileVal === 0) && appVal != null && appVal > 0) {
                              diffs.push({ field: `${my.year} Acres`, file: fileVal || 0, app: appVal });
                            }
                          });

                          // Max Eligibility check
                          // If file has explicit maxEligible field (R&H format), use that
                          // Otherwise calculate from max of year values (NAU format)
                          const fileMax = fr.fileMaxEligible != null
                            ? fr.fileMaxEligible
                            : (() => { const vals = Object.values(fr.yearData).filter(v => v != null && v > 0); return vals.length > 0 ? Math.max(...vals) : null; })();
                          const appMax = appMatch.maxAcres ?? 0;
                          if (fileMax != null && fileMax > 0 && Math.abs(fileMax - appMax) > 0.5) {
                            diffs.push({ field: 'Max Eligible', file: fileMax, app: appMax });
                          }

                          if (diffs.length) farmerReport.mismatches.push({ file: fr, app: appMatch, diffs });
                          else farmerReport.matches.push({ file: fr, app: appMatch });
                        });

                        appData.ppRows.forEach((ar, idx) => {
                          if (!appUsed.has(idx)) {
                            // Only flag as app-only if a matching crop+type exists in the file
                            // (so GSS won't be flagged if the file only has GSG rows)
                            const matchInFile = fileData.rows.some(fr => {
                              if (fr.crop !== ar.crop) return false;
                              if (fr.type && ar.type && fr.type !== ar.type) return false;
                              return true;
                            });
                            if (matchInFile) farmerReport.inAppOnly.push(ar);
                          }
                        });

                        if (farmerReport.mismatches.length || farmerReport.inFileOnly.length || farmerReport.inAppOnly.length) {
                          report.mismatches.push(farmerReport);
                        } else {
                          report.matches.push(farmerReport);
                        }
                      });

                      setAuditReport(report);
                      const totalIssues = report.mismatches.length + report.notInApp.length;
                      setAuditStatus({ok: totalIssues === 0, msg: totalIssues === 0
                        ? `✅ PP Audit complete — all ${report.matches.length} farmers match!`
                        : `PP Audit complete — ${report.matches.length} match, ${report.mismatches.length} discrepancies, ${report.notInApp.length} not in app`
                      });

                    } else {
                      // ═══ CROP COVERAGE AUDIT (existing logic) ═══
                      const fileByFarmer = {};
                      rows.forEach(row => {
                        const rawName = String(row[map.farmerName]||'').trim();
                        if (!rawName) return;
                        const normName = normFarmerName(rawName);
                        if (!fileByFarmer[normName]) fileByFarmer[normName] = { rawName, policyNumber: null, rows: [] };
                        if (map.policyNumber && row[map.policyNumber]) fileByFarmer[normName].policyNumber = String(row[map.policyNumber]).trim();
                        const plan = normPlan(map.plan ? row[map.plan] : '');
                        fileByFarmer[normName].rows.push({
                          county: (String(row[map.county]||'')).toUpperCase().trim(),
                          crop: (String(row[map.crop]||'')).toUpperCase().trim(),
                          practice: normP(map.practice ? row[map.practice] : null),
                          plan,
                          approvedYield: map.approvedYield ? normNum(row[map.approvedYield]) : null,
                          coverageLevel: map.coverageLevel ? normCov(row[map.coverageLevel]) : null,
                          priceElection: map.priceElection ? normNum(row[map.priceElection]) : null,
                          acres: map.acres ? normNum(row[map.acres]) : null,
                          grossPremium: map.grossPremium ? normNum(row[map.grossPremium]) : null,
                        });
                      });
                      const fileSummary = {};
                      Object.entries(fileByFarmer).forEach(([normName, data]) => {
                        const grouped = {};
                        data.rows.forEach(r => {
                          const isScoEco = r.plan === 'SCO-RP' || r.plan === 'ECO-RP';
                          const key = `${r.county}|${r.crop}|${r.practice}|${isScoEco ? r.plan : 'BASE'}`;
                          if (!grouped[key]) grouped[key] = { ...r, yields: [], count: 0, plan: isScoEco ? r.plan : 'RP' };
                          if (r.approvedYield != null) grouped[key].yields.push(r.approvedYield);
                          grouped[key].count++;
                        });
                        fileSummary[normName] = {
                          rawName: data.rawName, policyNumber: data.policyNumber,
                          coverages: Object.values(grouped).map(g => ({
                            county: g.county, crop: g.crop, practice: g.practice, plan: g.plan,
                            approvedYield: g.yields.length ? Math.round(g.yields.reduce((a,b)=>a+b,0)/g.yields.length) : null,
                            coverageLevel: g.coverageLevel, priceElection: g.priceElection,
                            acres: g.acres, grossPremium: g.grossPremium, unitCount: g.count,
                          }))
                        };
                      });
                      const appByFarmer = {};
                      allPolicies.forEach(pol => {
                        const f = allFarmerRows.find(fr => fr.id === pol.farmer_id);
                        if (!f) return;
                        const normName = f.name.trim().toUpperCase();
                        if (!appByFarmer[normName]) appByFarmer[normName] = { name: f.name, coverages: [] };
                        allCoverage.filter(c => c.policy_id === pol.id).forEach(c => {
                          appByFarmer[normName].coverages.push({
                            county: (c.county||'').toUpperCase(), crop: (c.crop||'').toUpperCase(),
                            practice: normP(c.practice), plan: c.plan || 'RP',
                            approvedYield: c.aph_yield, coverageLevel: c.coverage_level,
                            priceElection: c.price_election, acres: c.acres,
                            grossPremium: c.total_premium, unitCount: c.unit_count,
                          });
                        });
                      });
                      const report = { matches: [], mismatches: [], inFileOnly: [], inAppOnly: [], notInApp: [], auditType: 'crop' };
                      Object.entries(fileSummary).forEach(([normName, fileData]) => {
                        const appData = appByFarmer[normName];
                        if (!appData) { report.notInApp.push({ name: fileData.rawName, coverages: fileData.coverages }); return; }
                        const farmerReport = { name: appData.name, matches: [], mismatches: [], inFileOnly: [], inAppOnly: [] };
                        const appUsed = new Set();
                        fileData.coverages.forEach(fc => {
                          const isScoEco = fc.plan === 'SCO-RP' || fc.plan === 'ECO-RP';
                          const appMatch = appData.coverages.find((ac, idx) => {
                            if (appUsed.has(idx)) return false;
                            const countyMatch = ac.county === fc.county || normalizeCounty(ac.county) === normalizeCounty(fc.county);
                            const planMatch = isScoEco ? ac.plan === fc.plan : (ac.plan !== 'SCO-RP' && ac.plan !== 'ECO-RP');
                            return countyMatch && ac.crop === fc.crop && ac.practice === fc.practice && planMatch;
                          });
                          if (!appMatch) { farmerReport.inFileOnly.push(fc); return; }
                          appUsed.add(appData.coverages.indexOf(appMatch));
                          const diffs = [];
                          if (fc.approvedYield != null && appMatch.approvedYield != null && Math.abs(fc.approvedYield - appMatch.approvedYield) > 1)
                            diffs.push({ field: 'Yield', file: fc.approvedYield, app: appMatch.approvedYield });
                          if (fc.coverageLevel != null && appMatch.coverageLevel != null && Math.abs(fc.coverageLevel - appMatch.coverageLevel) > 0.005)
                            diffs.push({ field: 'Coverage', file: (fc.coverageLevel*100).toFixed(0)+'%', app: appMatch.coverageLevel ? (appMatch.coverageLevel*100).toFixed(0)+'%' : '—' });
                          if (fc.priceElection != null && appMatch.priceElection != null && Math.abs(fc.priceElection - appMatch.priceElection) > 0.01)
                            diffs.push({ field: 'Price', file: '$'+fc.priceElection, app: appMatch.priceElection ? '$'+appMatch.priceElection : '—' });
                          if (fc.acres != null && appMatch.acres != null && Math.abs(fc.acres - appMatch.acres) > 0.5)
                            diffs.push({ field: 'Acres', file: fc.acres, app: appMatch.acres });
                          if (diffs.length) farmerReport.mismatches.push({ file: fc, app: appMatch, diffs });
                          else farmerReport.matches.push({ file: fc, app: appMatch });
                        });
                        appData.coverages.forEach((ac, idx) => {
                          if (!appUsed.has(idx)) { const cropInFile = fileData.coverages.some(fc => fc.crop === ac.crop); if (cropInFile) farmerReport.inAppOnly.push(ac); }
                        });
                        if (farmerReport.mismatches.length || farmerReport.inFileOnly.length || farmerReport.inAppOnly.length) report.mismatches.push(farmerReport);
                        else report.matches.push(farmerReport);
                      });
                      setAuditReport(report);
                      const totalIssues = report.mismatches.length + report.notInApp.length;
                      setAuditStatus({ok: totalIssues === 0, msg: totalIssues === 0
                        ? `✅ Audit complete — all ${report.matches.length} farmers match perfectly!`
                        : `Audit complete — ${report.matches.length} farmers match, ${report.mismatches.length} have discrepancies, ${report.notInApp.length} not found in app`
                      });
                    }
                  } catch(err) { setAuditStatus({ok:false,msg:'Audit error: ' + err.message}); }
                }} style={{padding:'10px 28px',background:'#1a4a6a',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:14,cursor:'pointer'}}>
                  🔍 Run Audit (Read-Only)
                </button>
                <button onClick={() => { setAuditPreview(null); setAuditMapping({}); setAuditStatus(null); }}
                  style={{padding:'10px 20px',background:'#e4ece4',color:'#2d4a2d',border:'none',borderRadius:7,fontWeight:600,fontSize:13,cursor:'pointer'}}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Audit Report */}
          {auditReport && (
            <div>
              {/* Summary */}
              <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:20}}>
                {[
                  {label:'✅ Match',count:auditReport.matches.length,bg:'#e8f5e9',color:'#2d4a2d'},
                  {label:'⚠️ Discrepancies',count:auditReport.mismatches.length,bg:'#fff3e0',color:'#e65100'},
                  {label:'❌ Not in App',count:auditReport.notInApp.length,bg:'#fdecea',color:'#c62828'},
                ].map(s => (
                  <div key={s.label} style={{padding:'12px 20px',background:s.bg,borderRadius:8,textAlign:'center',minWidth:120}}>
                    <div style={{fontSize:24,fontWeight:800,color:s.color}}>{s.count}</div>
                    <div style={{fontSize:11,fontWeight:700,color:s.color}}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Discrepancies detail */}
              {auditReport.mismatches.length > 0 && (
                <div style={{marginBottom:20}}>
                  <div style={{fontWeight:800,fontSize:15,color:'#e65100',marginBottom:10}}>⚠️ Farmers with Discrepancies</div>
                  {auditReport.mismatches.map((fr, fi) => (
                    <div key={fi} style={{border:'1px solid #f0d0a0',borderRadius:8,marginBottom:8,overflow:'hidden'}}>
                      <div onClick={() => setAuditExpanded(prev => ({...prev, [fi]: !prev[fi]}))}
                        style={{padding:'10px 14px',background:'#fff8f0',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <span style={{fontWeight:700,color:'#1a2e1a'}}>{fr.name}</span>
                        <span style={{fontSize:12,color:'#e65100',fontWeight:600}}>
                          {fr.mismatches.length} mismatch{fr.mismatches.length!==1?'es':''} · {fr.inFileOnly.length} file-only · {fr.inAppOnly.length} app-only
                          {auditExpanded[fi] ? ' ▲' : ' ▼'}
                        </span>
                      </div>
                      {auditExpanded[fi] && (
                        <div style={{padding:'10px 14px'}}>
                          {fr.mismatches.map((m, mi) => (
                            <div key={mi} style={{marginBottom:8,padding:'6px 10px',background:'#fff3e0',borderRadius:6,fontSize:12}}>
                              <div style={{fontWeight:700}}>{m.file.county} · {m.file.crop} · {m.file.practice}{m.file.plan !== 'RP' ? ' · '+m.file.plan : ''}</div>
                              {m.diffs.map((d, di) => (
                                <div key={di} style={{marginTop:2}}>
                                  <span style={{color:'#e65100',fontWeight:600}}>{d.field}:</span>{' '}
                                  File says <strong>{d.file}</strong>, App has <strong>{d.app}</strong>
                                </div>
                              ))}
                            </div>
                          ))}
                          {fr.inFileOnly.map((r, ri) => (
                            <div key={'fo'+ri} style={{marginBottom:4,padding:'6px 10px',background:'#fdecea',borderRadius:6,fontSize:12}}>
                              <span style={{fontWeight:700,color:'#c62828'}}>In file but not in app:</span> {r.county} · {r.crop}{r.type ? '/'+r.type : ''} · {r.practice}
                              {auditReport.auditType === 'pp' && r.yearData
                                ? ` · Acres: ${Object.entries(r.yearData).sort((a,b)=>b[0]-a[0]).map(([y,v])=>`${y}:${v||0}`).join('/')}`
                                : auditReport.auditType !== 'pp' ? ` · Yield: ${r.approvedYield || '—'}` : ''}
                            </div>
                          ))}
                          {fr.inAppOnly.map((r, ri) => (
                            <div key={'ao'+ri} style={{marginBottom:4,padding:'6px 10px',background:'#e8eaf6',borderRadius:6,fontSize:12}}>
                              <span style={{fontWeight:700,color:'#1a237e'}}>In app but not in file:</span> {r.county} · {r.crop}{r.type ? '/'+r.type : ''} · {r.practice}
                              {auditReport.auditType === 'pp' && r.yearData
                                ? ` · Acres: ${Object.entries(r.yearData).sort((a,b)=>b[0]-a[0]).map(([y,v])=>`${y}:${v||0}`).join('/')}`
                                : auditReport.auditType !== 'pp' ? ` · Yield: ${r.approvedYield || '—'}` : ''}
                            </div>
                          ))}
                          {fr.matches.length > 0 && (
                            <div style={{marginTop:6,fontSize:11,color:'#60735f'}}>+ {fr.matches.length} matching {auditReport.auditType === 'pp' ? 'row' : 'coverage'}{fr.matches.length!==1?'s':''}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Not in app */}
              {auditReport.notInApp.length > 0 && (
                <div style={{marginBottom:20}}>
                  <div style={{fontWeight:800,fontSize:15,color:'#c62828',marginBottom:10}}>❌ Farmers in File but Not in App</div>
                  {auditReport.notInApp.map((fr, fi) => {
                    const rowCount = (fr.coverages || fr.rows || []).length;
                    return (
                      <div key={fi} style={{padding:'8px 14px',background:'#fdecea',borderRadius:6,marginBottom:4,fontSize:13}}>
                        <strong>{fr.name}</strong> — {rowCount} {auditReport.auditType === 'pp' ? 'PP' : 'coverage'} row{rowCount!==1?'s':''}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Perfect matches */}
              {auditReport.matches.length > 0 && (
                <div>
                  <div style={{fontWeight:800,fontSize:15,color:'#2d4a2d',marginBottom:10}}>✅ Farmers with Perfect Match ({auditReport.matches.length})</div>
                  <div style={{fontSize:12,color:'#60735f'}}>
                    {auditReport.matches.map(fr => fr.name).join(' · ')}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{marginTop:20,display:'flex',gap:10}}>
                <button onClick={() => {
                  // Generate a standalone HTML report in a new tab
                  const r = auditReport;
                  const now = new Date().toLocaleString();
                  const fileName = auditFile?.name || 'unknown';
                  let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>CloverAg Audit Report</title>
                    <style>
                      body{font-family:'Plus Jakarta Sans','Segoe UI',sans-serif;max-width:900px;margin:0 auto;padding:20px;color:#1a2e1a}
                      h1{color:#1a2e1a;border-bottom:3px solid #2d4a2d;padding-bottom:8px}
                      h2{color:#2d4a2d;margin-top:28px}
                      .summary{display:flex;gap:16px;margin:16px 0}
                      .card{padding:16px 24px;border-radius:8px;text-align:center;min-width:130px}
                      .card .num{font-size:28px;font-weight:800}
                      .card .lbl{font-size:12px;font-weight:700}
                      .match{background:#e8f5e9;color:#2d4a2d}
                      .warn{background:#fff3e0;color:#e65100}
                      .err{background:#fdecea;color:#c62828}
                      .farmer{border:1px solid #e0e0e0;border-radius:8px;margin:10px 0;overflow:hidden}
                      .farmer-header{padding:10px 14px;background:#f5f5f5;font-weight:700;font-size:14px}
                      .farmer-body{padding:10px 14px}
                      .diff{padding:6px 10px;margin:4px 0;border-radius:6px;font-size:12px}
                      .diff-warn{background:#fff3e0}
                      .diff-miss{background:#fdecea}
                      .diff-extra{background:#e8eaf6}
                      table{border-collapse:collapse;width:100%;font-size:12px;margin:8px 0}
                      th,td{padding:5px 10px;border:1px solid #e0e0e0;text-align:left}
                      th{background:#f0f4f0;font-weight:700}
                      .footer{margin-top:30px;padding-top:10px;border-top:1px solid #e0e0e0;font-size:11px;color:#999}
                      @media print{body{padding:10px}.summary{gap:8px}}
                    </style></head><body>`;
                  html += `<h1>🔍 CloverAg Audit Report</h1>`;
                  html += `<p style="color:#666;font-size:13px">File: <strong>${fileName}</strong> · Generated: ${now}</p>`;

                  // Summary
                  html += `<div class="summary">
                    <div class="card match"><div class="num">${r.matches.length}</div><div class="lbl">✅ Match</div></div>
                    <div class="card warn"><div class="num">${r.mismatches.length}</div><div class="lbl">⚠️ Discrepancies</div></div>
                    <div class="card err"><div class="num">${r.notInApp.length}</div><div class="lbl">❌ Not in App</div></div>
                  </div>`;

                  // Discrepancies
                  if (r.mismatches.length) {
                    html += `<h2>⚠️ Farmers with Discrepancies (${r.mismatches.length})</h2>`;
                    r.mismatches.forEach(fr => {
                      html += `<div class="farmer"><div class="farmer-header">${fr.name}</div><div class="farmer-body">`;
                      if (fr.mismatches.length) {
                        html += `<table><tr><th>County</th><th>Crop</th><th>Practice</th><th>Plan</th><th>Field</th><th>File Value</th><th>App Value</th></tr>`;
                        fr.mismatches.forEach(m => {
                          m.diffs.forEach((d, di) => {
                            html += `<tr${di===0?'':' style="border-top:none"'}><td>${di===0?m.file.county:''}</td><td>${di===0?m.file.crop:''}</td><td>${di===0?m.file.practice:''}</td><td>${di===0?m.file.plan:''}</td><td style="color:#e65100;font-weight:700">${d.field}</td><td><strong>${d.file}</strong></td><td><strong>${d.app}</strong></td></tr>`;
                          });
                        });
                        html += `</table>`;
                      }
                      fr.inFileOnly.forEach(r2 => {
                        html += `<div class="diff diff-miss"><strong>In file but not in app:</strong> ${r2.county} · ${r2.crop} · ${r2.practice} · Yield: ${r2.approvedYield||'—'}</div>`;
                      });
                      fr.inAppOnly.forEach(r2 => {
                        html += `<div class="diff diff-extra"><strong>In app but not in file:</strong> ${r2.county} · ${r2.crop} · ${r2.practice} · Yield: ${r2.approvedYield||'—'}</div>`;
                      });
                      if (fr.matches.length) html += `<div style="font-size:11px;color:#60735f;margin-top:6px">+ ${fr.matches.length} matching coverage(s)</div>`;
                      html += `</div></div>`;
                    });
                  }

                  // Not in app
                  if (r.notInApp.length) {
                    html += `<h2>❌ Farmers in File but Not in App (${r.notInApp.length})</h2>`;
                    r.notInApp.forEach(fr => {
                      const rc = (fr.coverages || fr.rows || []).length;
                      html += `<div class="diff diff-miss"><strong>${fr.name}</strong> — ${rc} ${r.auditType === 'pp' ? 'PP' : 'coverage'} row(s)</div>`;
                    });
                  }

                  // Perfect matches
                  if (r.matches.length) {
                    html += `<h2>✅ Perfect Matches (${r.matches.length})</h2>`;
                    html += `<p style="font-size:12px;color:#60735f">${r.matches.map(fr => fr.name).join(' · ')}</p>`;
                  }

                  html += `<div class="footer">CloverAg Audit Report · ${now}</div></body></html>`;

                  const w = window.open('', '_blank');
                  writePrintWindow(w, html);
                }} style={{padding:'10px 28px',background:'#1a4a6a',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:14,cursor:'pointer'}}>
                  📋 Open Report in New Tab
                </button>
                <button onClick={() => { setAuditReport(null); setAuditPreview(null); setAuditMapping({}); setAuditStatus(null); setAuditFile(null); setAuditExpanded({}); setAuditCropFilter({}); }}
                  style={{padding:'10px 20px',background:'#e4ece4',color:'#2d4a2d',border:'none',borderRadius:7,fontWeight:600,fontSize:13,cursor:'pointer'}}>
                  Start New Audit
                </button>
              </div>
            </div>
          )}
        </Card>
      )}


      {activeTab === 'lrpxlsx' && (
        <Card>
          <SectionTitle>RAIN & HAIL LRP AGENCY EXPORT (.xlsx)</SectionTitle>
          <p style={{margin:'0 0 14px',color:'#60735f',fontSize:13}}>
            Supports two R&H formats — auto-detected on upload. 
            Policy Lines export (e.g. policylines.xlsx): title row "Policy Lines -- [policy#] -- [name]" — imports all endorsements matched by policy number or name, includes actual end values and producer premiums. 
            Agency Data Set Builder export: Sort Name, LRP Coverage Price, LRP End Date, LRP Endorsement Length, LRP Number Of Head, LRP Target Weight columns. 
            Duplicate detection uses end date + number of head.
          </p>
          <input type="file" accept=".xlsx,.xls" onChange={e => { setLrpFile(e.target.files[0]); setLrpStatus(null); }}
            style={{marginBottom:12,display:'block'}} />
          {lrpFile && <div style={{fontSize:12,color:'#4a6e4a',marginBottom:8}}>📎 {lrpFile.name}</div>}
          {lrpStatus && (
            <div style={{padding:'9px 14px',background:lrpStatus.ok?'#e8f5e9':'#fdecea',
              color:lrpStatus.ok?'#2d4a2d':'#c62828',borderRadius:6,fontSize:13,fontWeight:600,marginBottom:10}}>
              {lrpStatus.msg}
            </div>
          )}
          <button onClick={async () => {
            if (!lrpFile) { setLrpStatus({ok:false,msg:'Please select a file first.'}); return; }
            try {
              // Load SheetJS via script tag if not already present
              if (!window.XLSX) {
                await new Promise((res, rej) => {
                  const s = document.createElement('script');
                  s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
                  s.onload = res; s.onerror = rej;
                  document.head.appendChild(s);
                });
              }
              const XLSX = window.XLSX;
              const buf = await lrpFile.arrayBuffer();
              // cellDates:true → dates come as JS Date objects, not Excel serial numbers (avoids 1969 bug)
              const wb  = XLSX.read(buf, { cellDates: true });
              const ws  = wb.Sheets[wb.SheetNames[0]];
              const firstCellRaw = ws["A1"] ? (ws["A1"].v || "") : "";
              const isPolicyLinesFormat = /policy\s*lines/i.test(String(firstCellRaw));
              const rows = isPolicyLinesFormat ? [] : window.XLSX.utils.sheet_to_json(ws, { defval: "", raw: true });

              // Helper: format date to MM/DD/YYYY
              // With cellDates:true, v is a JS Date object
              // With raw:false, v may be a pre-formatted string like "10/14/2025"
              const fmtDate = v => {
                if (!v) return '';
                if (v instanceof Date) {
                  // JS Date from SheetJS — use local date parts to avoid UTC shift
                  const yr  = v.getFullYear();
                  const mo  = v.getMonth() + 1;
                  const dy  = v.getDate();
                  if (yr < 1990 || yr > 2050) return '';  // guard against epoch zero dates
                  return mo.toString().padStart(2,'0') + '/' + dy.toString().padStart(2,'0') + '/' + yr;
                }
                // Already a string — validate it looks like a real date
                const d = new Date(v);
                if (isNaN(d) || d.getFullYear() < 1990) return String(v);
                return (d.getMonth()+1).toString().padStart(2,'0')+'/'+d.getDate().toString().padStart(2,'0')+'/'+d.getFullYear();
              };
              // Helper: "BENTLEY, ERIC" or "BENTLEY ERIC" → "Eric Bentley"
              const parseName = raw => {
                const s = String(raw||'').trim();
                if (!s) return s;
                // Check for "Last, First" format
                if (s.includes(',')) {
                  const [last, first] = s.split(',').map(p => p.trim());
                  const cap = w => w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : '';
                  return cap(first) + ' ' + cap(last);
                }
                // "LAST FIRST" — capitalize each word
                return s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
              };

              // Group by Sort Name
              const farmerMap = {};
              rows.forEach(row => {
                const rawName = String(row['Sort Name']||'').trim();
                if (!rawName) return;
                // Rows with no meaningful data (name-only header rows) — skip
                // Accept rows that have EITHER a sales date OR an end date OR a coverage price
                const salesDateVal = row['Sales Effective Date'] || row['Sales Date'] || row['Effective Date'] || '';
                const endDateVal   = row['LRP End Date'] || row['End Date'] || '';
                const covPriceVal  = row['LRP Coverage Price'] || row['Coverage Price'] || '';
                if (!salesDateVal && !endDateVal && !covPriceVal) return;
                const displayName = parseName(rawName);
                const key = rawName; // use raw as map key for consistency

                if (!farmerMap[key]) {
                  farmerMap[key] = {
                    id: Date.now() + Math.random(),
                    name: displayName,
                    address:'', phone:'', policyNumber:'',
                    cropYear: CURRENT_CROP_YEAR,
                    agent:'', agency:'', insurer:'',
                    policyTypes:['lrp'],
                    approvedYields:[], ppEligibility:[],
                    lrpPolicies:[{
                      id:'lrp-'+Date.now()+Math.random(),
                      commodity: '', type:'', county:'',
                      endorsements:[],
                    }],
                    drpPolicies:[], prfPolicies:[],
                  };
                }
                const pol = farmerMap[key].lrpPolicies[0];
                // Read commodity directly from column if it exists; fall back to target weight
                const commodityRaw = String(row['LRP Commodity']||row['Commodity']||'').trim();
                const twt = Number(row['LRP Target Weight']||0);
                // Target weight cutoff: < 5 cwt = Feeder Cattle, >= 5 cwt = Fed Cattle
                // Note: some exports use 0.99 which represents a weight ratio, not cwt — treat as feeder
                const commodity = commodityRaw || (twt < 5 ? 'Feeder Cattle' : 'Fed Cattle');
                // Update policy-level commodity (last seen wins — most farmers have one type)
                if (commodity) pol.commodity = commodity;

                const rawGrossPrem = row['Premium'] ?? row['Total Premium'] ?? row['Gross Premium'];
                const rawProdPrem  = row['Adjusted Premium'] ?? row['Net Premium'] ?? row['Producer Premium'] ?? row['Farmer Paid Premium'];
                const rawSubsidy   = row['Subsidy'] ?? row['Premium Subsidy'] ?? row['Government Subsidy'];
                const rawIndemnity = row['Payable Indemnity'] ?? row['Indemnity'];
                const grossPremVal = rawGrossPrem  != null && rawGrossPrem  !== '' ? Number(rawGrossPrem) : 0;
                const prodPremVal  = rawProdPrem   != null && rawProdPrem   !== '' ? Number(rawProdPrem)  : grossPremVal;
                const subsidyVal   = rawSubsidy    != null && rawSubsidy    !== '' ? Number(rawSubsidy)   : Math.max(0, grossPremVal - prodPremVal);
                const indemnityVal = rawIndemnity  != null && rawIndemnity  !== '' ? Number(rawIndemnity)  : 0;
                // Flexible column name matching for head, weight, etc.
                const headVal      = Number(row['LRP Number Of Head'] || row['Number Of Head'] || row['Head'] || row['# Of Head'] || row['LRP # Of Head'] || 0);
                const weightVal    = Number(row['LRP Target Weight'] || row['Target Weight'] || row['Weight'] || 0);
                const weeksVal     = Number(row['LRP Endorsement Length'] || row['Endorsement Length'] || row['Weeks'] || 0);
                const covPrice     = Number(row['LRP Coverage Price'] || row['Coverage Price'] || 0);
                // Derive sales date: try columns, then calculate from endDate - weeks
                let salesDateStr = fmtDate(row['Sales Effective Date'] || row['Sales Date'] || row['Effective Date'] || '');
                if (!salesDateStr && endDateVal && weeksVal > 0) {
                  const ed = new Date(endDateVal instanceof Date ? endDateVal : endDateVal);
                  if (!isNaN(ed)) {
                    const sd = new Date(ed.getTime() - weeksVal * 7 * 24 * 60 * 60 * 1000);
                    salesDateStr = fmtDate(sd);
                  }
                }
                pol.endorsements.push({
                  lineId:        pol.endorsements.length + 1,
                  commodity,
                  salesDate:     salesDateStr,
                  endDate:       fmtDate(row['LRP End Date'] || row['End Date']),
                  weeksOut:      weeksVal,
                  head:          headVal,
                  targetWeight:  weightVal,
                  coveragePrice: covPrice,
                  insuredValue:  Math.round(covPrice * headVal * weightVal),
                  premium:         grossPremVal,
                  producerPremium: prodPremVal,
                  subsidy:         subsidyVal,
                  payableIndemnity: indemnityVal,
                  actualEndValue: 0,
                });
              });

              let incoming = [];

              if (isPolicyLinesFormat) {
                // ── POLICY LINES FORMAT PARSER ──
                // Row 0: "Policy Lines  --  MP-0922500 -- MULLIGAN FAMILY FARM LLC"
                // Row 1: column headers
                // Rows 2+: data rows (skip rows where Line ID is blank/non-numeric = totals rows)
                const titleCell = String(firstCellRaw).trim();
                // Extract policy number and name from title
                const titleMatch = titleCell.match(/policy\s*lines\s*--+\s*([\w\-]+)\s*--+\s*(.+)/i);
                const extractedPolicyNum = titleMatch ? titleMatch[1].trim() : '';
                const extractedName      = titleMatch ? titleMatch[2].trim() : '';

                if (!extractedName) {
                  setLrpStatus({ok:false, msg:'Could not read farmer name from title row. Expected: "Policy Lines -- <POLICY#> -- <NAME>"'});
                  return;
                }

                // SheetJS sheet_to_json with header:1 gives raw arrays; use header row (row index 1) as keys
                const rawRows = window.XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
                // rawRows[0] = title, rawRows[1] = headers, rawRows[2+] = data
                const headers = rawRows[1] || [];
                const colIdx = {};
                headers.forEach((h, i) => { if (h) colIdx[String(h).trim()] = i; });

                const normDate2 = v => {
                  if (!v) return '';
                  if (v instanceof Date) {
                    const yr = v.getFullYear(), mo = v.getMonth()+1, dy = v.getDate();
                    if (yr < 1990 || yr > 2050) return '';
                    return mo.toString().padStart(2,'0')+'/'+dy.toString().padStart(2,'0')+'/'+yr;
                  }
                  const s = String(v).trim();
                  const d = new Date(s);
                  if (isNaN(d) || d.getFullYear() < 1990) return s;
                  return (d.getMonth()+1).toString().padStart(2,'0')+'/'+d.getDate().toString().padStart(2,'0')+'/'+d.getFullYear();
                };

                const endorsements = [];
                for (let i = 2; i < rawRows.length; i++) {
                  const r = rawRows[i];
                  const lineId = r[colIdx['Line ID']];
                  // Skip totals/summary rows — Line ID must be a number
                  if (!lineId || isNaN(Number(lineId))) continue;

                  const commodity   = String(r[colIdx['Commodity']]||'').trim() || 'Feeder Cattle';
                  const practiceRaw = String(r[colIdx['Practice']]||'').trim();
                  // Extract weeks from Practice like "Endorsement Ending in May 2026   (43 Weeks)"
                  const weeksMatch  = practiceRaw.match(/\((\d+)\s*weeks?\)/i);
                  const weeksOut    = weeksMatch ? Number(weeksMatch[1]) : 0;
                  const salesDate   = normDate2(r[colIdx['Sales Effective Date']]);
                  const endDate     = normDate2(r[colIdx['End Date']]);
                  const head        = Number(r[colIdx['Number of Head']]||0);
                  const targetWt    = Number(r[colIdx['Target Weight']]||0);
                  const covPrice    = Number(r[colIdx['Coverage Price']]||0);
                  const insuredVal  = Number(r[colIdx['Insured Value']]||0) || Math.round(covPrice * head * targetWt);
                  const grossPrem   = Number(r[colIdx['Premium']]||0);
                  const prodPrem    = Number(r[colIdx['Producer Premium']]||0) || grossPrem;
                  const subsidyAmt  = Math.max(0, grossPrem - prodPrem);
                  const actualEnd   = Number(r[colIdx['Actual End Value']]||0);
                  const county      = String(r[colIdx['County']]||'').trim();
                  const typeVal     = String(r[colIdx['Type']]||'').trim();

                  if (!endDate && !covPrice && !head) continue; // fully empty row

                  endorsements.push({
                    lineId:          Number(lineId),
                    commodity,
                    type:            typeVal,
                    county,
                    salesDate,
                    endDate,
                    weeksOut,
                    head,
                    targetWeight:    targetWt,
                    coveragePrice:   covPrice,
                    insuredValue:    insuredVal,
                    premium:         grossPrem,
                    producerPremium: prodPrem,
                    subsidy:         subsidyAmt,
                    payableIndemnity: 0,
                    actualEndValue:  actualEnd,
                  });
                }

                if (!endorsements.length) {
                  setLrpStatus({ok:false, msg:'No endorsement rows found in policy lines file.'}); return;
                }

                // Match to existing farmer by policy number first, then by name
                // Build a synthetic farmer object — onImport will merge it against the live farmer list
                const matchedFarmer = farmers.find(f =>
                  f.policyNumber === extractedPolicyNum ||
                  (f.lrpPolicies||[]).some(p => p.policyNumber === extractedPolicyNum)
                ) || farmers.find(f => f.name.toUpperCase() === extractedName.toUpperCase());

                incoming = [{
                  id: matchedFarmer?.id || null,
                  name: matchedFarmer?.name || extractedName,
                  address: matchedFarmer?.address || '',
                  phone: matchedFarmer?.phone || '',
                  policyNumber: extractedPolicyNum || matchedFarmer?.policyNumber || '',
                  cropYear: CURRENT_CROP_YEAR,
                  agent: matchedFarmer?.agent || '',
                  agency: matchedFarmer?.agency || '',
                  insurer: matchedFarmer?.insurer || '',
                  policyTypes: [...new Set([...(matchedFarmer?.policyTypes||[]), 'lrp'])],
                  approvedYields:  matchedFarmer?.approvedYields  || [],
                  ppEligibility:   matchedFarmer?.ppEligibility   || [],
                  drpPolicies:     matchedFarmer?.drpPolicies     || [],
                  prfPolicies:     matchedFarmer?.prfPolicies     || [],
                  lrpPolicies: [{
                    id: matchedFarmer?.lrpPolicies?.[0]?.id || ('lrp-'+Date.now()),
                    policyNumber: extractedPolicyNum,
                    commodity: endorsements[0]?.commodity || 'Feeder Cattle',
                    type: '', county: '',
                    endorsements,
                  }],
                }];

              } else {
                // ── EXISTING AGENCY EXPORT FORMAT ──
                incoming = Object.values(farmerMap).filter(f => f.lrpPolicies[0].endorsements.length > 0);
              }

              if (!incoming.length) { setLrpStatus({ok:false,msg:'No LRP endorsements found in file.'}); return; }
              const merged = onImport(incoming, false);
              setLrpFile(null);
              setLrpStatus({ok:true, msg:`Saving LRP data for ${incoming.length} farmer(s)...`});
              // Save each merged farmer to Supabase so data survives refresh
              let saved = 0, failed = 0;
              for (const farmer of (merged?.length ? merged : incoming)) {
                try { await saveFarmerToSupabase(farmer); saved++; }
                catch(err) { console.warn('LRP save failed for', farmer.name, err); failed++; }
              }
              setLrpStatus({ok: failed === 0, msg: failed === 0
                ? `Saved LRP data for ${saved} farmer(s).`
                : `Saved ${saved}, failed ${failed}. Check console.`
              });
            } catch(e) {
              setLrpStatus({ok:false, msg:'Error reading file: '+e.message});
            }
          }} style={{padding:'11px 28px',background:'#2d4a2d',color:'#fff',border:'none',
            borderRadius:7,fontWeight:800,fontSize:13,letterSpacing:'0.08em',cursor:'pointer'}}>
            IMPORT LRP EXCEL
          </button>
          <p style={{margin:'12px 0 0',fontSize:11,color:'#aaa'}}>
            Note: Premium and actual end values are not in this export — those come from individual LRP PDFs.
            This import gives you coverage prices, head counts, and end dates for all endorsements.
          </p>
        </Card>
      )}

      {activeTab === 'lrpindemnity' && (
        <Card>
          <SectionTitle>LRP AREA PLAN REPORT — INDEMNITY IMPORT</SectionTitle>
          <p style={{margin:'0 0 14px',color:'#60735f',fontSize:13}}>
            Upload the RMA Area Plan Report (.xlsx). Matches by policy number + endorsement number and writes
            <strong>payable indemnity</strong> only. Actual ending values come from the policy lines files — this report does not update AEV.
          </p>
          <input type="file" accept=".xlsx,.xls" onChange={e => { setLrpIndFile(e.target.files[0]); setLrpIndStatus(null); }}
            style={{marginBottom:12,display:'block'}} />
          {lrpIndFile && <div style={{fontSize:12,color:'#4a6e4a',marginBottom:8}}>📎 {lrpIndFile.name}</div>}
          {lrpIndStatus && (
            <div style={{padding:'9px 14px',
              background:lrpIndStatus.ok?'#e8f5e9':'#fdecea',
              color:lrpIndStatus.ok?'#2d4a2d':'#c62828',
              borderRadius:6,fontSize:13,fontWeight:600,marginBottom:10,whiteSpace:'pre-line'}}>
              {lrpIndStatus.msg}
            </div>
          )}
          <button onClick={async () => {
            if (!lrpIndFile) { setLrpIndStatus({ok:false,msg:'Please select a file first.'}); return; }
            setLrpIndStatus({ok:true,msg:'Reading file...'});
            try {
              if (!window.XLSX) {
                await new Promise((res,rej) => {
                  const s = document.createElement('script');
                  s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
                  s.onload = res; s.onerror = rej;
                  document.head.appendChild(s);
                });
              }
              const buf = await lrpIndFile.arrayBuffer();
              const wb  = window.XLSX.read(buf, { cellDates: true });
              const ws  = wb.Sheets[wb.SheetNames[0]];
              const rawRows = window.XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });

              // Col indices — Area Plan Report fixed columns:
              // 3=PolicyNum 9=EndorsementNum 16=CovPrice 25=Indemnity
              // AEV (col 18) is NOT used — Area Plan Report shows multiple rows per endorsement
              // with different AEVs. Policy lines files are the authoritative AEV source.
              const C = { pnum:3, endNum:9, indemnity:25 };

              // Deduplicate by policy+endorsement_num — take the max indemnity across rows
              // (indemnity should be consistent per endorsement but take max to be safe)
              const groups = {};
              for (let i = 1; i < rawRows.length; i++) {
                const r = rawRows[i];
                const pnum   = String(r[C.pnum]||'').trim().replace(/^\s+/,'');
                const endNum = Number(r[C.endNum]);
                if (!pnum || isNaN(endNum) || endNum === 0) continue;
                const key = `${pnum}|${endNum}`;
                const ind = Number(r[C.indemnity]||0);
                if (!groups[key] || ind > groups[key].indemnity) {
                  groups[key] = { pnum, endNum, indemnity: ind };
                }
              }
              const endorsements = Object.values(groups);

              if (!endorsements.length) {
                setLrpIndStatus({ok:false,msg:'No settled rows found in file (all AEV = 0).'}); return;
              }

              setLrpIndStatus({ok:true,msg:`Parsed ${endorsements.length} endorsements. Fetching DB records...`});

              // Get all distinct policy numbers from file
              const pnums = [...new Set(endorsements.map(e => e.pnum))];

              // Fetch matching policies from DB
              const pnumFilter = pnums.map(p => `policy_number.eq.${p}`).join(',');
              const policies = await sbFetch('policies', { filters: [`or=(${pnumFilter})`] });
              if (!policies.length) {
                setLrpIndStatus({ok:false,msg:'No matching policies found in DB.\n' + pnums.join(', ')}); return;
              }

              // Fetch all lrp_endorsements for those policies
              const policyIds = policies.map(p => p.id);
              const idFilter = 'policy_id=in.(' + policyIds.join(',') + ')';
              const dbEnds = await sbFetch('lrp_endorsements', { filters: [idFilter] });

              // Build lookup: policyNumber -> policyId
              const pnumToId = {};
              policies.forEach(p => { pnumToId[p.policy_number.trim()] = p.id; });

              // Match and update
              let updated = 0, skipped = 0, notFound = 0;
              const notFoundList = [];

              for (const e of endorsements) {
                const policyId = pnumToId[e.pnum];
                if (!policyId) { notFound++; notFoundList.push(`${e.pnum} #${e.endNum}`); continue; }

                // Match by policy_id + line_id (endorsement number)
                const dbRow = dbEnds.find(d =>
                  d.policy_id === policyId && Number(d.line_id) === e.endNum
                );
                if (!dbRow) { notFound++; notFoundList.push(`${e.pnum} #${e.endNum}`); continue; }

                const newIndemnity = e.indemnity;
                const existingInd  = dbRow.payable_indemnity;

                // Skip if indemnity already matches
                if (existingInd == newIndemnity) { skipped++; continue; }

                // Only write payable_indemnity — never touch actual_end_value (policy lines are authoritative for AEV)
                const patch = { payable_indemnity: newIndemnity };

                try {
                  await fetch(`${SUPABASE_URL}/rest/v1/lrp_endorsements?id=eq.${dbRow.id}`, {
                    method: 'PATCH',
                    headers: { ...getAuthHeaders(), 'Prefer': 'return=minimal' },
                    body: JSON.stringify(patch),
                  });
                  updated++;
                } catch(err) {
                  console.warn('PATCH failed for', e.pnum, e.endNum, err);
                  skipped++;
                }
              }

              const msg = [
                `✅ Updated ${updated} endorsements`,
                skipped > 0  ? `⏭ Skipped ${skipped} (no new data or already current)` : null,
                notFound > 0 ? `⚠️ ${notFound} not found in DB: ${notFoundList.slice(0,5).join(', ')}${notFoundList.length>5?' …':''}` : null,
              ].filter(Boolean).join('\n');

              setLrpIndFile(null);
              setLrpIndStatus({ok: notFound === 0 || updated > 0, msg});

            } catch(e) {
              setLrpIndStatus({ok:false,msg:'Error: ' + e.message});
            }
          }} style={{padding:'11px 28px',background:'#2d4a2d',color:'#fff',border:'none',
            borderRadius:7,fontWeight:800,fontSize:13,letterSpacing:'0.08em',cursor:'pointer'}}>
            IMPORT INDEMNITY DATA
          </button>
          <p style={{margin:'12px 0 0',fontSize:11,color:'#aaa'}}>
            Matches by policy number + endorsement number. Writes payable indemnity only — AEV is not touched.
          </p>
        </Card>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DROPBOX SYNC SCREEN
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// YIELD TRACKER — APH AUTO-FEED
// When an APH is imported, write/update production_history rows from the
// "Production Reporting Unit Summary" table (Page 2 of NAU APH documents).
// ─────────────────────────────────────────────────────────────────────────────
async function saveYieldFromAPH(ext, farmerId) {
  const units = ext.productionReportUnits;
  if (!Array.isArray(units) || units.length === 0) return { inserted: 0, skipped: 0 };

  // Extract numeric policy number
  // NAU format: NY-944-6096532-26 → 6096532
  // R&H format: MP-0870819 → 870819
  const policyFull = ext.policyNumber || '';
  let policyNum;
  const nauMatch = policyFull.match(/-(\d+)-\d+$/);
  const rhMatch  = policyFull.match(/MP-0*(\d+)$/i);
  if (nauMatch) policyNum = nauMatch[1];
  else if (rhMatch) policyNum = rhMatch[1];
  else policyNum = policyFull;

  const aipCode = ext.aip === 'RAIN_AND_HAIL' ? 'RH' : 'NAU';

  let inserted = 0, skipped = 0;

  for (const unit of units) {
    if (!unit.county || !unit.crop || unit.reportedCropYear == null) { skipped++; continue; }

    const crop    = String(unit.crop).toUpperCase().trim();
    const county  = String(unit.county).toUpperCase().trim();
    const type    = String(unit.type || 'GSG').toUpperCase().trim();
    const acres   = Number(unit.acres || 0);
    const prod    = Number(unit.totalProduction || 0);
    const recType = String(unit.productionRecordType || '').toUpperCase();
    const cropYear= Number(unit.reportedCropYear);

    // Exclusion logic
    let excluded = false, reason = null;
    if (type === 'GSS')    { excluded = true; reason = 'SILAGE'; }
    else if (acres === 0)  { excluded = true; reason = 'ZERO_ACRES'; }
    else if (prod === 0)   { excluded = true; reason = 'ZERO_PRODUCTION'; }
    else if (recType === 'N') { excluded = true; reason = 'APPRAISAL'; } // Non-loss appraisal

    const row = {
      farmer_id:         farmerId || null,
      farmer_name_raw:   ext.farmerName || null,
      policy_number:     policyNum,
      crop_year:         cropYear,
      crop:              crop,
      county:            county,
      state:             'NY',
      acres:             acres,
      production:        prod,
      unit_of_measure:   type === 'GSS' ? 'TON' : 'BU',
      aip:               aipCode,
      excluded_from_avg: excluded,
      exclusion_reason:  reason,
      source_file:       'APH_Database_auto',
    };

    // Upsert: update if policy+crop+county+year already exists, else insert
    // Using POST with on-conflict resolution via Prefer header
    try {
      const existing = await sbFetch('production_history', {
        filters: [
          `policy_number=eq.${policyNum}`,
          `crop=eq.${crop}`,
          `county=eq.${county}`,
          `crop_year=eq.${cropYear}`,
          `aip=eq.${aipCode}`,
        ]
      });

      if (existing && existing.length > 0) {
        // Skip — don't overwrite existing data
        skipped++;
        continue;
      } else {
        // Insert new row
        await fetch(`${SUPABASE_URL}/rest/v1/production_history`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Prefer': 'return=minimal' },
          body: JSON.stringify(row),
        });
        inserted++;
      }
    } catch(e) {
      console.warn('saveYieldFromAPH row error:', e.message, row);
      skipped++;
    }
  }
  return { inserted, skipped };
}

// ─────────────────────────────────────────────────────────────────────────────
// INDEMNITY TRACKER — Book-wide indemnity payments view
// ─────────────────────────────────────────────────────────────────────────────
// Whole-farm base guarantee for one crop/year from raw crop_coverage rows. Excludes
// ECO/SCO/ARPI top-up bands (area plans, no base acres). Derives bushel guarantee from
// aph×cov×acres; when a unit has a dollar liability but null acres (the per-county
// crop_coverage gap — e.g. Anderson's Monroe wheat) it back-derives bu = liability÷price
// so the unit still counts; anything with neither is surfaced as an incomplete unit
// rather than silently dropped.
// ⚠ THE HARVEST GUARANTEE IS ON PLANTED ACRES (2026-10-18). crop_coverage.liability is
// INSURED = planted + prevent plant, so liability ÷ price carried every PP acre's liability
// into the bushel guarantee a production claim is measured against — CY2026 corn RP alone
// held $2.43M of PP liability across 67 cells. PP acres are paid as PP, never as a harvest
// shortfall. `unitRows` is the year's crop_coverage_units rows for these policies (or null
// where none exist, e.g. coverage_snapshots); each row is split through plantedSplit(), THE
// one planted-split rule — RMA's per-unit sum where the units reconcile, the PP-factor
// model otherwise. The PP liability is returned beside the guarantee, never inside it.
const CLAIM_UNITS_SELECT = 'policy_id,crop_year,county,crop,plan,type_code,practice_code,is_prevent_plant,acres,liability';
function claimUnitsByPolicy(units) {
  const byPol = {};
  for (const u of (units || [])) {
    if (!u || !u.policy_id) continue;
    const m = (byPol[u.policy_id] ||= {});
    const k = cuKey(u.county, u.crop, u.plan, u.type_code, u.practice_code);
    (m[k] ||= []).push(u);
  }
  return byPol;
}
function summarizeClaimCoverage(rows, crop, year, unitRows) {
  const unitsByPolicy = claimUnitsByPolicy(unitRows);
  const target = String(crop).toUpperCase();
  const isMatch = c => { const u = String(c || '').toUpperCase(); return target === 'WHEAT' ? (u === 'WHEAT' || u === 'WINTER WHEAT') : u === target; };
  const isTopUp = p => /ECO|SCO|ARPI/i.test(String(p || ''));
  const base = (rows || []).filter(r => isMatch(r.crop) && !isTopUp(r.plan));
  const topUp = (rows || []).filter(r => isMatch(r.crop) && isTopUp(r.plan));
  // Corn insures as GRAIN (type GSG, bushels) OR SILAGE (type GSS/026, tons) — different
  // products in different units. NEVER add bushels and tons into one guarantee (the
  // units-of-measure invariant — a blended number is neither). Keep them apart: guaranteeBu
  // for grain, guaranteeTon for silage. Everything else (wheat/soy) is grain/bu, so isSilageRow
  // is false and this collapses to the old bushel-only behaviour.
  const isSilageRow = r => { const t = String(r.type || '').toUpperCase(); return t === 'GSS' || t === '026'; };
  // ⚠ PP ACRES ARE TRACKED SEPARATELY BECAUSE YIELD PER ACRE MUST NOT DIVIDE BY THEM.
  // crop_coverage.acres is INSURED = planted + prevent plant (CLAUDE.md, "THERE ARE THREE
  // ACRE FIGURES"). Bushels only ever come off PLANTED ground, so dividing production by
  // the insured figure understates yield by exactly the PP share — and on a farm that is
  // half prevent plant it FLIPS THE SIGN of the comparison against APH.
  // LANG FARMS LLC CY2026 corn: 72.7 insured = 36.7 planted + 36.0 PP. The Claim Manager
  // showed 4,913 bu / 72.7 = 67.6 bu/ac, "-33.4 vs APH 101" — reading as a bad crop and a
  // likely claim. Over planted acres it is 133.9 bu/ac, +32.9 OVER his APH. Same class as
  // the appraisal-coverage denominator fixed the same day, on the screen that decides
  // whether to open a claim.
  let guar = 0, acres = 0, ppAcres = 0, grainPpAcres = 0, guarTon = 0, silageAcres = 0, liability = 0, ppLiability = 0, ppLiabilityModelled = false;
  const incomplete = [], plans = new Set(), counties = new Set(), units = [];
  base.forEach(r => {
    const aph = Number(r.aph_yield) || 0, cov = Number(r.coverage_level) || 0, ac = Number(r.acres) || 0;
    // clamped: a pp figure above the insured acres is a bad row, not negative planted ground
    const pp = Math.min(ac, Math.max(0, Number(r.pp_acres) || 0));
    const lib = Number(r.liability) || 0, pe = Number(r.price_election) || 0;
    const plan = String(r.plan || 'RP').toUpperCase();
    const sil = isSilageRow(r);
    const unit = sil ? 'ton' : 'bu';
    plans.add(plan);
    if (r.county) counties.add(r.county);
    const px = pe > 0 ? pe : (RMA_SPRING_PRICES[year]?.[target] || 0);
    // The planted half of this coverage, through THE shared rule. pp = 0 → identity.
    const split = plantedSplit(r, pp, r.policy_id ? (unitsByPolicy[r.policy_id] || null) : null);
    const plantedAc = ac > 0 ? (split.acres != null ? Number(split.acres) : Math.max(0, ac - pp)) : 0;
    const plantedLib = (lib > 0 && split.liability != null) ? Math.max(0, Number(split.liability)) : (lib > 0 ? lib : 0);
    const ppLib = lib > 0 ? Math.max(0, lib - plantedLib) : 0;
    if (ppLib > 0) { ppLiability += ppLib; if (!split.exact) ppLiabilityModelled = true; }
    const allPP = pp > 0 && ac > 0 && plantedAc <= 0;   // every acre prevent plant: no harvest guarantee, legitimately 0
    let uBu = null, uAc = null, uLib = null, via = 'ok';
    if (sil) {
      // Silage tons = APH(ton/ac) × cov × PLANTED acres — no $/ton price to derive from
      // liability, and a PP acre grows no silage to count against a guarantee.
      if (aph > 0 && cov > 0 && ac > 0) { uBu = aph * cov * plantedAc; uAc = ac; uLib = lib || null; guarTon += uBu; silageAcres += ac; ppAcres += pp; liability += lib; }
      else { if (lib > 0) { uLib = lib; liability += lib; } via = 'none'; incomplete.push({ county: r.county, via: 'none' }); }
    } else {
      // Grain bushel guarantee via the SHARED guaranteeBushels() — liability ÷ projected price
      // (= the SOI Marketable Guarantee, acre-weighted), APH×cov×acres only when a row has no
      // liability. Same helper the farmer card uses, so the two can never drift apart again.
      // NO statedGuaranteeBu() here, deliberately — this surface keeps the derived path
      // and its ~0.35% CAT overstatement (see the note on guaranteeBushels in RULES).
      // Units ARE in scope now, but only for the planted split; RMA's stated guarantee
      // (statedGuaranteeBu) is deliberately not adopted in the same change — this keeps the
      // claim guarantee equal to Σ planted liability ÷ elected price, checkable by hand.
      uBu = allPP ? 0 : guaranteeBushels(plantedLib > 0 ? plantedLib : null, px, aph, cov, plantedAc > 0 ? plantedAc : ac, (r.priceElectionPct ?? r.price_election_pct));
      if (uBu != null) {
        uLib = lib || null; guar += uBu;
        if (lib > 0) liability += lib;
        if (ac > 0) { uAc = ac; acres += ac; ppAcres += pp; grainPpAcres += pp; }
        else if (lib > 0) { via = 'liability'; incomplete.push({ county: r.county, via: 'liability' }); }
      } else { via = 'none'; incomplete.push({ county: r.county, via: 'none' }); }   // no liability & no usable APH
    }
    units.push({ county: r.county || '—', plan, aph: aph || null, cov: cov || null, acres: uAc, ppAcres: uAc != null ? pp : null, plantedAcres: uAc != null ? plantedAc : null, buGuar: uBu, liability: uLib, ppLiability: ppLib || null, plantedExact: !!split.exact, via, unit });
  });
  return {
    base, units, guaranteeBu: guar || null, guaranteeTon: guarTon || null,
    acres: acres || null, silageAcres: silageAcres || null, liability: liability || null,
    // Inside `liability` (INSURED) and OUTSIDE guaranteeBu/guaranteeTon. Paid as PP.
    ppLiability: ppLiability || null, ppLiabilityModelled,
    plantedLiability: liability ? Math.max(0, liability - ppLiability) : null,
    // INSURED minus PP. null when there are no acres at all, 0 when every acre is PP —
    // those are different facts and a caller must be able to tell them apart.
    ppAcres: (acres || silageAcres) ? ppAcres : null,
    plantedAcres: (acres || silageAcres) ? Math.max(0, (acres + silageAcres) - ppAcres) : null,
    // The GRAIN half alone: the acres a bushel guarantee covers and a bushel figure must cover.
    // Silage (026) acres are settled in tons; reading grain production against them withheld
    // a grain claim read for as long as any silage acre lacked tons.
    grainPlantedAcres: acres ? Math.max(0, acres - grainPpAcres) : (silageAcres ? 0 : null),
    projectedPrice: RMA_SPRING_PRICES[year]?.[target] || null,
    plans: [...plans], counties: [...counties], incomplete,
    hasTopUp: topUp.length > 0, topUpPlans: [...new Set(topUp.map(r => String(r.plan).toUpperCase()))],
  };
}

// ── Claims Manager: campaign taxonomy + workflow pipeline ────────────────────
// A "campaign" is (crop_year, claim_type). Harvest types map to a grain crop;
// Prevent Plant is its own roster (pp_eligibility). Emoji keep the picker scannable.
const CLAIM_TYPES = {
  PREVENT_PLANT: { label: 'Prevent Plant',   kind: 'pp',      crop: null,       emoji: '🌧️' },
  WHEAT_HARVEST: { label: 'Wheat Harvest',    kind: 'harvest', crop: 'Wheat',    emoji: '🌾' },
  CORN_HARVEST:  { label: 'Corn Harvest',     kind: 'harvest', crop: 'Corn',     emoji: '🌽' },
  SOY_HARVEST:   { label: 'Soybean Harvest',  kind: 'harvest', crop: 'Soybeans', emoji: '🫘' },
};
const CLAIM_WORKFLOW = ['To Review', 'Contacted', 'Awaiting Tickets', 'Over — No Claim', 'Under — Needs Claim', 'Claim Filed', 'Paid / Closed'];
const claimTypeForCrop = (crop) => Object.keys(CLAIM_TYPES).find(k => CLAIM_TYPES[k].crop === crop) || null;
// Season-aware default campaign so the board opens on what we're actually working, instead of
// forever on wheat. Windows (Kevin): Jun 1–Jul 10 Prevent Plant, Jul 11–Aug 15 Wheat Harvest,
// Aug 16–May Spring Crops (corn/soy — the long harvest + claim-settling season). Only the
// DEFAULT — the campaign buttons still switch freely. (new Date() is fine in the app; only
// workflow scripts forbid it.)
function defaultClaimCampaign(d = new Date()) {
  const m = d.getMonth() + 1, day = d.getDate();               // month 1–12, day 1–31
  if (m === 6 || (m === 7 && day <= 10)) return 'PREVENT_PLANT';                 // Jun 1 – Jul 10
  if ((m === 7 && day >= 11) || (m === 8 && day <= 15)) return 'WHEAT_HARVEST';  // Jul 11 – Aug 15
  return 'CORN_HARVEST';                                                          // Aug 16 – May: spring crops
}

// ⚠ `rmaNetFromTickets` AND `computeHarvestClaim` LIVE IN src/claims/claimEstimate.js
// (moved 2026-09-23, unchanged) and are imported at the top of this file.
//
// Kevin: "We will calculate what we think the claim should be and then see what actually
// arrives when the claim is processed and paid by AIP." The estimate FROZEN into
// `claim_estimates` has to be the same arithmetic the Claim Manager roster displays, or
// the latch compares the carrier against a number no screen ever showed and a
// disagreement says nothing about whether the AIP made a mistake. The module imports only
// its sibling rules (harvestRule.js, claimCrops.js) so a checker can load the shipped source.
//
// ⚠ AND ClaimCheckView DOES NOT RE-DERIVE IT EITHER (2026-10-19). The drill-in used to carry
// its own inline copy of the shrink, the guarantee price and the indemnity — and counted
// appraised bushels the roster never did. Both now hand every production SOURCE, by name, to
// computeHarvestClaim; `check-harvest-one-engine` asserts neither screen adds production up.
//
// Do not re-declare either here — `check-claim-estimate` asserts this file holds no
// second copy, for the reason this codebase has already paid for twice: two guarantee
// engines disagreed about one farm's forward-sell ceiling, and three news handlers
// drifted twice.

// ─────────────────────────────────────────────────────────────────────────────
// CLAIM CHECK — "do we need to open a claim?" screener (Claims page tab).
// Enter (or import from a Bushel scale-ticket CSV) total production for a farm/crop,
// compare against the RP bushel guarantee AND revenue guarantee. Wheat's harvest price
// isn't final until RMA publishes (~mid-Aug), so we run a live estimate off the Sept
// wheat futures July average and swap to the RMA final the moment it's entered. Whole-
// farm per crop (v1); optional-unit losses can hide inside a whole-farm surplus, so
// this is a screener, not the adjuster's settlement.
// ─────────────────────────────────────────────────────────────────────────────
function ClaimCheckView({ farmers, isMobile, lockFarmerId, lockCrop, lockYear, jdData }) {
  const locked = !!lockFarmerId;   // rendered as a drill-in from the Claims Manager roster
  const [farmerId, setFarmerId] = useState(lockFarmerId || '');
  // The guarantee derivation and the JD cross-check are COLLAPSED by default — the figures
  // they lead with are now stated once at the top of the page, and what is left behind each
  // control is the working. Not persisted: a reader who opened one farm's detail has not
  // asked to see every farm's.
  const [guarOpen, setGuarOpen] = useState(false);
  const [jdOpen, setJdOpen] = useState(false);
  // Planted acres with no production figure, reported up by the evidence panel so the scaled yield
  // below reads the SAME answer as the headline. null = not read yet (treated as not complete).
  const [acctRead, setAcctRead] = useState(null);
  const [crop, setCrop] = useState(lockCrop || 'Wheat');
  const [year, setYear] = useState(lockYear || CURRENT_CROP_YEAR);
  const [cov, setCov] = useState(null);
  // Every crop this farm insures in this crop year, from its own coverage rows —
  // never a typed list. Empty until a farm is picked, which is why the picker falls
  // back to the grain crops rather than rendering an empty select.
  const [cropsHeld, setCropsHeld] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [harvest, setHarvest] = useState(null);     // {price, days, lastDay, isFinal}
  const [loading, setLoading] = useState(false);
  const [imp, setImp] = useState(null);
  // What the AIP's own APH database says this farm reported. NOT delivered
  // bushels - see src/reportedProduction.js. null = never reported, which is a
  // different fact from reporting zero.
  const [reported, setReported] = useState(null);             // CSV import status {status,msg}
  const [silage, setSilage] = useState([]);         // silage_appraisals rows (corn, chopped acres)
  const [silageImp, setSilageImp] = useState(null); // appraisal PDF read status
  const [includeSilage, setIncludeSilage] = useState(true); // count appraised bu in production?
  const [insuredFields, setInsuredFields] = useState([]); // policy_fields (corn) — FSN-level appraisal coverage
  // Which crop year those insured acres actually came from. It is the DENOMINATOR of
  // "% of insured acres appraised", so if it had to be borrowed from another year the
  // page has to say so rather than print a percentage of the wrong acreage.
  const [insuredFieldsYear, setInsuredFieldsYear] = useState(null);
  const [savedNote, setSavedNote] = useState(false);   // manual inputs persisted?
  // A failed read or save of THIS farm's claim entries, by table. A failed read must not
  // look like "nothing entered" — and must not arm the autosave, or the blank form would
  // overwrite the stored figures. A failed save must not say "Saved".
  const [persistErr, setPersistErr] = useState({});    // { inputsLoad, countyLoad, inputsSave, countySave }
  const loadedKeyRef = useRef('');                      // guards autosave until this farm's inputs load
  // production-to-count inputs (bushels)
  const [storedBu, setStoredBu] = useState('');
  const [seedBu, setSeedBu] = useState('');
  const [fedBu, setFedBu] = useState('');
  const [manualDelivered, setManualDelivered] = useState('');   // override the ticket-derived delivered bu
  const [shrinkPerPt, setShrinkPerPt] = useState(1.2);          // RMA shrink %/pt above base moisture
  const [baseMoist, setBaseMoist] = useState(13.5);             // RMA standard moisture for the crop
  // ── BUSHELS BY COUNTY (Kevin, 2026-09-28) ──────────────────────────────────
  // *"The only way we will be able to attribute bushels to a county is from John Deere
  // data if that is connected, or by us manually entering bushels or scale tickets by
  // county. Make sure you allow us a spot to manually enter bushels by county."*
  //
  // ⚠ A CLAIM SETTLES PER UNIT AND A SCALE TICKET CANNOT SAY WHERE THE BUSHELS CAME
  // FROM — all 1,907 on file carry `buyer`/`location`/`contract_ref`, which is the
  // ELEVATOR's location, not the farm's county. So this is typed, or accepted from the
  // machine, and never derived.
  //
  // ⚠ KEYED BY COUNTY WITH A STRING VALUE, and '' is NOT 0. An empty box means the
  // county has not been stated; a typed 0 is a total loss, which is the largest
  // indemnity the policy can pay. Collapsing those two is the one mistake here that
  // costs a grower real money, so the state keeps them distinct all the way down.
  const [countyBu, setCountyBu] = useState({});        // { COUNTY: '12345' | '' }
  const [countySrc, setCountySrc] = useState({});      // { COUNTY: 'manual' | 'jd' }
  const countyLoadedRef = useRef('');                  // guards autosave until this farm's rows land

  const cropU = crop.toUpperCase();
  // ── WHAT THIS CROP IS MEASURED IN, AND WHICH CLAIM TEST IT SETTLES ON ──────
  // Both are per crop and both were hardcoded to grain. `claimCrops` is the one rule;
  // see its header for the measurement. The basis comes from the PLANS ON THE POLICY,
  // not from the crop — oats in this book carries six YP rows and one RP row, so a
  // crop-keyed table would give one of those growers the wrong test.
  const cropChoices = useMemo(() => {
    // The farm's own coverage once it has loaded. Before a farm is picked there is no
    // book to read, so the grain crops stand in — an empty select reads as a broken
    // page, and this is the only place a typed list is allowed to appear.
    if (cropsHeld.length) return cropsHeld.map(c => ({ ...c, label: claimCropLabel(c.crop) }));
    return GRAIN_CROPS.map(c => ({ crop: c.toUpperCase(), label: c, acres: null, liability: null, plans: [], basis: null, unit: 'bu', placeholder: true }));
  }, [cropsHeld]);
  const cropHeld = useMemo(() => cropsHeld.find(c => c.crop === cropU) || null, [cropsHeld, cropU]);
  // The unit for THIS crop's grain/base product. Silage is tracked separately below it
  // by summarizeClaimCoverage, which never adds bushels to tons.
  const cropUnit = claimUnit(cropU, null);
  const cropUnitAssumed = unitIsAssumed(cropU, null);
  // The claim test. Derived from the loaded coverage's own plans where we have them,
  // so a farm whose potatoes are APH reads yield even though another farm's might not.
  const cropBasis = cov && cov.plans && cov.plans.length ? coverageBasis(cov.plans) : (cropHeld ? { basis: cropHeld.basis, mixed: cropHeld.mixed } : null);
  const basisKind = cropBasis ? cropBasis.basis : null;
  const isRevenueBasis = basisKind === 'revenue';
  const cropBasisNote = basisKind ? basisNote(basisKind, cropU) : null;
  const selFarmer = useMemo(() => farmers.find(f => f.id === farmerId), [farmers, farmerId]);
  const sortedFarmers = useMemo(() => [...(farmers || [])].sort((a, b) => String(a.name).localeCompare(String(b.name))), [farmers]);
  const farmerDbIds = selFarmer?.allFarmerDbIds?.length ? selFarmer.allFarmerDbIds : (farmerId ? [farmerId] : []);

  const bu = n => (n == null || !isFinite(n)) ? '—' : Math.round(n).toLocaleString();
  const money = n => (n == null || !isFinite(n)) ? '—' : '$' + Math.round(n).toLocaleString();
  const px = n => (n == null || !isFinite(n)) ? '—' : '$' + Number(n).toFixed(2);

  async function loadAll() {
    if (!farmerDbIds.length) { setCov(null); setTickets([]); setHarvest(null); return; }
    setLoading(true);
    try {
      const pols = await sbFetch('policies', { select: 'id,crop_year', filters: [`farmer_id=in.(${farmerDbIds.join(',')})`] }).catch(() => []);
      const pids = (pols || []).filter(p => Number(p.crop_year) === Number(year)).map(p => p.id);
      let cc = [];
      if (pids.length) cc = await sbFetch('crop_coverage', { select: 'policy_id,county,crop,type,type_code,practice_code,plan,aph_yield,coverage_level,acres,pp_acres,price_election,liability,price_election_pct', filters: [`policy_id=in.(${pids.join(',')})`] }).catch(() => []);
      const tks = await sbFetch('grain_delivery_tickets', { filters: [`farmer_id=in.(${farmerDbIds.join(',')})`], order: 'delivery_date.desc' }).catch(() => []);
      const ph = await sbFetch('production_history', { select: REPORTED_PRODUCTION_SELECT, filters: [`farmer_id=in.(${farmerDbIds.join(',')})`, `crop_year=eq.${year}`] }).catch(() => []);
      const cfg = HARVEST_DISCOVERY[cropU];
      const sym = harvestContractSym(crop, year);
      let bars = [];
      if (cfg && sym) bars = await sbFetch('futures_contract_history', { select: 'bar_date,close', filters: [`commodity=eq.${cfg.commodity}`, `contract_sym=eq.${sym}`] }).catch(() => []);
      // The units carry RMA's own planted/PP split — without them the PP liability stays
      // inside the harvest guarantee (or is modelled at the PP factor).
      let cu = [];
      if (pids.length) cu = await sbFetch('crop_coverage_units', { select: CLAIM_UNITS_SELECT, filters: [`policy_id=in.(${pids.join(',')})`, `crop_year=eq.${year}`] }).catch(() => []);
      setCov(summarizeClaimCoverage(cc, crop, year, cu));
      // ⚠ THE CROP PICKER IS BUILT FROM THIS FARM'S OWN COVERAGE, and the rows are
      // already in hand — `cc` is every crop on the farm's policies for the year, and
      // summarizeClaimCoverage merely filters it. The picker used to read GRAIN_CROPS,
      // the GRAIN MARKETING list, which is correctly three crops there and left eleven
      // crops and $16.3M of CY2026 liability unselectable (potatoes $7.5M, grapes $6.7M,
      // onions, dry beans, cabbage, processing beans, peaches, grain sorghum, apples,
      // oats, triticale). A crop that cannot be selected reads as a crop with no claim
      // exposure.
      setCropsHeld(claimCropsInBook(cc));
      setTickets(tks || []);
      setReported(reportedProductionForCrop(ph || [], crop));
      setHarvest(resolveHarvestPrice(crop, year, bars));
      // Silage appraisals (corn only) — appraised grain production on chopped acres.
      if (cropU === 'CORN') {
        const sil = await sbFetch('silage_appraisals', { filters: [`farmer_id=in.(${farmerDbIds.join(',')})`, `crop_year=eq.${year}`] }).catch(() => []);
        setSilage(sil || []);
        // Insured corn fields (FSN + acres) — the denominator for "% of insured acres appraised".
        // Ask for THIS year first. The old read was deliberately unscoped ("policy_fields
        // isn't reliably year-scoped"), which was true while the table held one year and
        // becomes a duplicate-FSN bug the moment the M13 backfill writes another. Falling
        // back to the whole set keeps today's behaviour exactly — a 2025 claim check
        // currently gets the 2026 fields — and starts returning the right year's acres as
        // soon as that year is backfilled. `insuredFieldsYear` records which it was, so a
        // denominator borrowed from another year is never silent.
        let pf = await sbFetch('policy_fields', { select: 'fsn,field_id,acres,pp_acres,county,insured_county,unit_number,sub_unit,crop,crop_year,type_code', filters: [`farmer_id=in.(${farmerDbIds.join(',')})`, `crop_year=eq.${year}`] }).catch(() => []);
        let pfYear = year;
        if (!(pf || []).length) {
          pf = await sbFetch('policy_fields', { select: 'fsn,field_id,acres,pp_acres,county,insured_county,unit_number,sub_unit,crop,crop_year,type_code', filters: [`farmer_id=in.(${farmerDbIds.join(',')})`] }).catch(() => []);
          pfYear = (pf || []).reduce((m, f) => (f.crop_year != null && (m == null || f.crop_year > m) ? f.crop_year : m), null);
        }
        setInsuredFieldsYear(pfYear);
        setInsuredFields((pf || []).filter(f => /CORN/i.test(f.crop || '')));
      } else { setSilage([]); setInsuredFields([]); }
      // Restore saved manual inputs for this farm/crop/year (survives reloads);
      // reset to defaults when none, so a prior farm's entries don't carry over.
      let savedRows = [], inputsLoadErr = null;
      try { savedRows = await sbFetch('claim_check_inputs', { filters: [`farmer_id=eq.${farmerId}`, `crop=eq.${crop}`, `crop_year=eq.${year}`] }); }
      catch (e) { inputsLoadErr = String((e && e.message) || e); }
      const s = (savedRows || [])[0];
      setManualDelivered(s?.delivered_override != null ? String(s.delivered_override) : '');
      setStoredBu(s?.stored_bu != null ? String(s.stored_bu) : '');
      setSeedBu(s?.seed_bu != null ? String(s.seed_bu) : '');
      setFedBu(s?.fed_bu != null ? String(s.fed_bu) : '');
      setShrinkPerPt(s?.shrink_per_pt != null ? Number(s.shrink_per_pt) : 1.2);
      setBaseMoist(s?.base_moisture != null ? Number(s.base_moisture) : 13.5);
      setSavedNote(!!s);
      // Bushels already attributed to a county for this farm/crop/year. Reset to {} when
      // none, so a previous farm's entries can never carry over onto this one's claim.
      let cpRows = [], countyLoadErr = null;
      try {
        cpRows = await sbFetch('claim_county_production', {
          filters: [`farmer_id=eq.${farmerId}`, `crop=eq.${crop}`, `crop_year=eq.${year}`],
        });
      } catch (e) { countyLoadErr = String((e && e.message) || e); }
      const cb = {}, cs = {};
      for (const r of cpRows || []) {
        const k = String(r.county || '').trim().toUpperCase();
        if (!k) continue;
        // A stored NULL stays '' — "not stated" survives the round trip.
        cb[k] = r.bushels == null ? '' : String(r.bushels);
        cs[k] = r.source || 'manual';
      }
      setCountyBu(cb); setCountySrc(cs);
      setPersistErr({ inputsLoad: inputsLoadErr, countyLoad: countyLoadErr });
      // Autosave is armed ONLY for a table whose read succeeded — otherwise the blank form
      // a failed read leaves behind would be written over the figures already stored.
      countyLoadedRef.current = countyLoadErr ? '' : `${farmerId}|${crop}|${year}`;
      loadedKeyRef.current = inputsLoadErr ? '' : `${farmerId}|${crop}|${year}`;
    } finally { setLoading(false); }
  }
  useEffect(() => { loadAll(); }, [farmerId, crop, year, selFarmer]);   // eslint-disable-line
  // When embedded as a drill-in, follow the campaign/roster selection from props.
  useEffect(() => { if (lockFarmerId != null) setFarmerId(lockFarmerId); }, [lockFarmerId]);
  useEffect(() => { if (lockCrop) setCrop(lockCrop); }, [lockCrop]);
  useEffect(() => { if (lockYear) setYear(lockYear); }, [lockYear]);
  // Debounced autosave of the manual inputs. Guarded by loadedKeyRef so it never
  // writes a prior farm's values under a newly-selected farm before its load lands.
  useEffect(() => {
    const key = `${farmerId}|${crop}|${year}`;
    if (!farmerId || loadedKeyRef.current !== key) return;
    const t = setTimeout(async () => {
      try {
        await sbUpsert('claim_check_inputs', [{
          farmer_id: farmerId, crop, crop_year: year,
          delivered_override: manualDelivered === '' ? null : Number(manualDelivered),
          stored_bu: storedBu === '' ? null : Number(storedBu),
          seed_bu: seedBu === '' ? null : Number(seedBu),
          fed_bu: fedBu === '' ? null : Number(fedBu),
          shrink_per_pt: shrinkPerPt, base_moisture: baseMoist,
          updated_at: new Date().toISOString(),
        }], 'farmer_id,crop,crop_year');
        setSavedNote(true);
        setPersistErr((p) => ({ ...p, inputsSave: null }));
      } catch (e) {
        // Still on screen, but NOT stored — say so, never "Saved".
        setSavedNote(false);
        setPersistErr((p) => ({ ...p, inputsSave: String((e && e.message) || e) }));
      }
    }, 700);
    return () => clearTimeout(t);
  }, [farmerId, crop, year, manualDelivered, storedBu, seedBu, fedBu, shrinkPerPt, baseMoist]);   // eslint-disable-line

  // Debounced autosave of the per-county bushels, on its own guard so a slow
  // claim_county_production read can never write a prior farm's counties onto this one.
  //
  // ⚠ AN EMPTY BOX WRITES NULL, NOT 0. `bushels` is nullable precisely so "not stated"
  // survives; a 0 would read downstream as a total loss on that county.
  useEffect(() => {
    const key = `${farmerId}|${crop}|${year}`;
    if (!farmerId || countyLoadedRef.current !== key) return;
    const t = setTimeout(async () => {
      const rows = Object.keys(countyBu).map((county) => ({
        farmer_id: farmerId, crop, crop_year: year, county,
        bushels: countyBu[county] === '' || countyBu[county] == null ? null : Number(countyBu[county]),
        uom: cropU === 'CORN SILAGE' ? 'ton' : 'bu',
        source: countySrc[county] || 'manual',
        updated_at: new Date().toISOString(),
      }));
      if (!rows.length) return;
      try {
        await sbUpsert('claim_county_production', rows, 'farmer_id,crop,crop_year,county');
        setPersistErr((p) => ({ ...p, countySave: null }));
      } catch (e) {
        // The entries are on screen and NOT stored — say so.
        setPersistErr((p) => ({ ...p, countySave: String((e && e.message) || e) }));
      }
    }, 700);
    return () => clearTimeout(t);
  }, [farmerId, crop, year, countyBu, countySrc, cropU]);   // eslint-disable-line

  async function onImport(file) {
    if (!file || !farmerId) return;
    setImp({ status: 'busy', msg: 'Reading…' });
    try {
      if (!window.XLSX) await new Promise((res, rej) => { const s = document.createElement('script'); s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js'; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
      const buf = await file.arrayBuffer();
      const wb = window.XLSX.read(buf, { cellDates: false, raw: false });
      const rows = window.XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '', raw: false });
      const parsed = parseGrainTicketRows(rows, farmerId);
      if (!parsed.length) { setImp({ status: 'err', msg: 'No valid tickets found in the file.' }); return; }
      // Dedup preview keys on buyer|ticket_id (matches the DB constraint) so a second
      // elevator's ticket that happens to share a number isn't miscounted as a dupe.
      const existingRows = await sbFetch('grain_delivery_tickets', { filters: [`farmer_id=eq.${farmerId}`], select: 'ticket_id,buyer' }).catch(() => []);
      const existing = new Set((existingRows || []).map(r => `${r.buyer || ''}|${r.ticket_id}`));
      const fresh = parsed.filter(p => !existing.has(`${p.buyer || ''}|${p.ticket_id}`));
      await grainImportTickets(parsed);
      await loadAll();
      const newBu = fresh.reduce((s, t) => s + (t.net_bushels || 0), 0);
      setImp({ status: 'ok', msg: fresh.length ? `${fresh.length} new load${fresh.length > 1 ? 's' : ''} · ${Math.round(newBu).toLocaleString()} bu imported` : `No new loads — all ${parsed.length} already on file` });
    } catch (e) { setImp({ status: 'err', msg: e.message || 'Import failed' }); }
  }

  // Undo a bad CSV upload — delete every scale ticket that came in on that upload (grouped by
  // imported_at). Deletes by row id so it's exact regardless of ticket_id/buyer overlap.
  async function deleteBatch(batch) {
    if (!batch?.ids?.length) return;
    if (!window.confirm(`Delete this upload — ${batch.n} load${batch.n > 1 ? 's' : ''}, ${Math.round(batch.bu).toLocaleString()} bu${batch.buyer ? ` (${batch.buyer})` : ''}? This removes those scale tickets.`)) return;
    setImp({ status: 'busy', msg: 'Deleting upload…' });
    try {
      for (let i = 0; i < batch.ids.length; i += 100) await sbDelete('grain_delivery_tickets', `id=in.(${batch.ids.slice(i, i + 100).join(',')})`);
      await loadAll();
      setImp({ status: 'ok', msg: `Deleted ${batch.n} load${batch.n > 1 ? 's' : ''} · ${Math.round(batch.bu).toLocaleString()} bu removed` });
    } catch (e) { setImp({ status: 'err', msg: e.message || 'Delete failed' }); }
  }
  const fmtUploadTime = (ts) => { try { return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return String(ts || '').slice(0, 16); } };

  // Read an AIP corn/silage appraisal worksheet PDF → per-field appraised production.
  async function onUploadAppraisal(file) {
    if (!file || !farmerId) return;
    setSilageImp({ status: 'busy', msg: 'Checking appraisal…' });
    try {
      // The document's identity is its BYTES. The same worksheet re-saved under another name
      // used to land as a second set of rows and double the appraised production.
      const sha = await fileSha256(file);
      const ids = farmerDbIds.length ? farmerDbIds : [farmerId];
      // ⚠ NOT .catch(() => []). A read that failed cannot prove the worksheet is new, and
      // treating it as "nothing on file" is exactly how a duplicate gets written.
      const existing = await sbFetch('silage_appraisals', { filters: [`farmer_id=in.(${ids.join(',')})`] });
      const dup = sameContentOnFile(existing, sha);
      if (dup) {
        const when = dup.uploadedAt ? new Date(dup.uploadedAt).toLocaleDateString() : 'earlier';
        setSilageImp({ status: 'err', msg: `This appraisal is already on file (uploaded ${when}${dup.sourceFile ? ` as ${dup.sourceFile}` : ''}). Nothing was added.` });
        return;
      }
      setSilageImp({ status: 'busy', msg: 'Reading appraisal…' });
      const parsed = await extractSilageAppraisal(file);
      const wrongFarm = await appraisalPolicyMismatch(parsed, farmerId, year);
      if (wrongFarm) { setSilageImp({ status: 'err', msg: wrongFarm }); return; }
      const incoming = silageAppraisalRows(parsed, farmerId, year, file.name);
      if (!incoming.length) { setSilageImp({ status: 'err', msg: 'No appraised fields found in that PDF.' }); return; }
      // A DIFFERENT worksheet on units this farm already has appraisals for. Whether it is a
      // re-issue (replace) or more ground (add) is not something the two documents can settle
      // — appraisal lines carry no stable identity across forms — so the person decides.
      const ov = appraisalOverlap(existing, incoming);
      if (ov.conflicts.length) {
        const files = ov.files.map(f => `${f.sourceFile} (${f.rows} line${f.rows === 1 ? '' : 's'}, ${Math.round(f.bu).toLocaleString()} bu)`).join('; ');
        setSilageImp({ status: 'confirm',
          msg: `This farm already has appraisals for the same unit${ov.units.length === 1 ? '' : 's'} from another file: ${files}. Replace those lines with this worksheet, or add it alongside?`,
          pending: { file, sha, parsed, replaceIds: ov.conflicts.map(r => r.id).filter(Boolean) } });
        return;
      }
      await finishAppraisalUpload({ file, sha, parsed, replaceIds: [] }, false);
    } catch (e) { setSilageImp({ status: 'err', msg: e.message || 'Appraisal read failed' }); }
  }

  // Store the original, write its rows, and — only on an explicit Replace — remove the lines
  // it supersedes. Order matters: the PDF is stored BEFORE any row is written (a row with no
  // original is the defect), and the old lines go only AFTER the new ones are in, so a failed
  // write never leaves the unit with neither.
  async function finishAppraisalUpload(p, replace) {
    const { file, sha, parsed } = p;
    setSilageImp({ status: 'busy', msg: replace ? 'Replacing appraisal…' : 'Saving appraisal…' });
    try {
      const first = silageAppraisalRows(parsed, farmerId, year, file.name)[0];
      const cy = (first && Number(first.crop_year)) || year;
      const storagePath = appraisalStoragePath(farmerId, cy, sha, file.name, new Date());
      // ⚠ A LIMITED AGENT REACHES THIS UPLOAD (Claim Manager, role 'agent') and could always
      // write the ROWS. Storing the PDF needs sql/099's agent policies; until they are applied
      // the bucket refuses an agent, and refusing the whole upload would take away what they
      // could do before. So an admin's failed store stops the upload; an agent's records
      // the rows with no original and says so.
      let stored = storagePath;
      try {
        await sbStorageUpload('farmer-documents', storagePath, file);
      } catch (e) {
        if (isCurrentUserAdmin()) throw e;
        stored = null;
      }
      const rows = await saveSilageAppraisal(parsed, farmerId, year, file.name, { sha256: sha, storagePath: stored });
      if (!rows.length) { setSilageImp({ status: 'err', msg: 'No appraised fields found in that PDF.' }); return; }
      const replaced = replace ? (p.replaceIds || []) : [];
      for (let i = 0; i < replaced.length; i += 100) {
        await sbDelete('silage_appraisals', `id=in.(${replaced.slice(i, i + 100).join(',')})`);
      }
      await loadAll();
      const grainBu = rows.filter(r => r.appraised_bu != null).reduce((s, r) => s + (r.appraised_bu || 0), 0);
      // The reader is told HOW it was read. A figure lifted out of the form's own text can
      // be checked against the form; one a model reported is a different kind of number,
      // and the difference belongs on screen rather than in a column nobody opens.
      const how = parsed && parsed._parser === 'text' ? 'read from the form' : 'extracted';
      const skipped = parsed && parsed.totals && parsed.totals.withoutAcreage
        ? ` · ${parsed.totals.withoutAcreage} with no acreage, not counted` : '';
      // A field block the reader could not tie to a Field ID is named on screen, never
      // dropped without a word (Kevin, 2026-11-02): check it against the worksheet.
      const unread = parsed && parsed.totals && parsed.totals.refused
        ? ` · ${parsed.totals.refused} field${parsed.totals.refused === 1 ? '' : 's'} could not be read, not saved: check the worksheet` : '';
      setSilageImp({ status: 'ok', msg: `${rows.length} field${rows.length > 1 ? 's' : ''} ${how} · ${Math.round(grainBu).toLocaleString()} appraised bu${skipped}${unread}${replaced.length ? ` · replaced ${replaced.length} earlier line${replaced.length === 1 ? '' : 's'}` : ''}${stored ? ' · original PDF saved' : ' · original PDF NOT saved (storage refused it)'}` });
    } catch (e) { setSilageImp({ status: 'err', msg: e.message || 'Appraisal read failed' }); }
  }

  // ── Production to count ───────────────────────────────────────────────────
  const cropTickets = useMemo(() => (tickets || []).filter(t => t.crop === crop && Number(t.crop_year) === Number(year)), [tickets, crop, year]);
  // Group this crop's tickets by upload (imported_at, to the minute) so a wrong CSV can be
  // deleted as one batch. Each batch carries its row ids for an exact delete.
  const uploadBatches = useMemo(() => {
    const m = {};
    (cropTickets || []).forEach(t => {
      const k = t.imported_at ? String(t.imported_at).slice(0, 16) : 'unknown';
      const b = (m[k] = m[k] || { key: k, imported_at: t.imported_at, ids: [], n: 0, bu: 0, buyers: new Set() });
      b.ids.push(t.id); b.n++; b.bu += Number(t.net_bushels) || 0; if (t.buyer) b.buyers.add(t.buyer);
    });
    return Object.values(m).map(b => ({ ...b, buyer: [...b.buyers].join(', ') })).sort((a, b) => String(a.imported_at || '').localeCompare(String(b.imported_at || '')));
  }, [cropTickets]);
  const buyers = useMemo(() => [...new Set(cropTickets.map(t => t.buyer).filter(Boolean))], [cropTickets]);
  // Provenance. A load off the Bushel feed and one typed from a CSV are both
  // real, but ONLY the CSV path carries moisture and test weight - the Bushel
  // scale-ticket API has no quality field at all (checked against their spec).
  // Say which is which, so an empty quality column reads as "the feed does not
  // send it" rather than as missing data.
  const ticketSourceNote = useMemo(() => {
    const c = { bushel: 0, csv: 0, other: 0 };
    for (const t of cropTickets) {
      const s = String(t.source || '').toLowerCase();
      if (s === 'bushel') c.bushel++; else if (s === 'csv') c.csv++; else c.other++;
    }
    const parts = [];
    if (c.bushel) parts.push(c.bushel + ' from the Bushel feed');
    if (c.csv) parts.push(c.csv + ' from CSV');
    if (c.other) parts.push(c.other + ' other');
    return parts.join(' · ');
  }, [cropTickets]);

  // ── Moving a load to another crop year ───────────────────────────────────
  // The date rule (grainDeliveryCropYear) is right almost always. The case it
  // gets wrong is real: old-crop grain coming out of a bin after the new crop's
  // harvest month reads as new-crop production, which on a claim is the wrong
  // crop year. This is the manual correction for that.
  //
  // The assignment is HELD BY A DATABASE TRIGGER (sql/039), not here. Both
  // writers - the CSV import and the hourly Bushel promotion - upsert crop_year
  // and would otherwise overwrite it, the Bushel one every hour. Guarding it in
  // two places is the two-copies failure this repo keeps paying for.
  const [showLoads, setShowLoads] = useState(false);
  const [selectedLoads, setSelectedLoads] = useState(() => new Set());
  const [assignYear, setAssignYear] = useState(String(year));
  const [assigning, setAssigning] = useState('');
  const [loadErr, setLoadErr] = useState(null);
  const [loadLimit, setLoadLimit] = useState(50);
  useEffect(() => { setSelectedLoads(new Set()); setAssignYear(String(year)); setLoadLimit(50); }, [crop, year, farmerId]);

  // EVERY crop year, not just the selected one - a load that landed in the wrong
  // year has to be reachable from the year you are working in.
  const cropAllYears = useMemo(() => (tickets || []).filter(t => t.crop === crop), [tickets, crop]);
  const lockedCount = useMemo(() => cropAllYears.filter(t => t.crop_year_locked).length, [cropAllYears]);
  const loadsByYear = useMemo(() => {
    const m = {};
    for (const t of cropAllYears) {
      const k = t.crop_year == null ? 'null' : String(t.crop_year);
      (m[k] = m[k] || []).push(t);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => String(b.delivery_date || '').localeCompare(String(a.delivery_date || '')));
    }
    // Selected year first, then newest down - you are usually working one claim.
    return Object.entries(m).sort((a, b) => {
      if (Number(a[0]) === Number(year)) return -1;
      if (Number(b[0]) === Number(year)) return 1;
      return String(b[0]).localeCompare(String(a[0]));
    });
  }, [cropAllYears, year]);
  const assignableYears = useMemo(() => {
    const ys = new Set([Number(year), Number(year) - 1, Number(year) + 1]);
    for (const t of cropAllYears) if (t.crop_year != null) ys.add(Number(t.crop_year));
    return [...ys].filter(y => isFinite(y)).sort((a, b) => b - a);
  }, [cropAllYears, year]);

  // No sbPatch exists in this file, and adding a shared one for a single caller
  // would widen a surface for no reason. authedFetch is the primitive both
  // sbUpsert and sbDelete are built on.
  async function patchTickets(ids, patch) {
    if (!ids.length) return;
    for (let i = 0; i < ids.length; i += 100) {
      const slice = ids.slice(i, i + 100);
      const res = await authedFetch(
        `${SUPABASE_URL}/rest/v1/grain_delivery_tickets?id=in.(${slice.join(',')})`,
        { method: 'PATCH', headers: { Prefer: 'return=minimal' }, body: JSON.stringify(patch) }
      );
      if (!res.ok) throw new Error(await res.text());
    }
  }

  // TWO statements on purpose. The trigger holds crop_year on any row that is
  // already locked, so a deliberate reassignment has to unlock first. An import
  // carries crop_year but never crop_year_locked, so it can never take this path.
  async function assignCropYear(ids, toYear) {
    if (!ids.length || !isFinite(toYear)) return;
    setAssigning('assign'); setLoadErr(null);
    try {
      await patchTickets(ids, { crop_year_locked: false });
      await patchTickets(ids, { crop_year: toYear, crop_year_locked: true });
      setSelectedLoads(new Set());
      await loadAll();
    } catch (e) { setLoadErr(String(e.message || e).slice(0, 160)); } finally { setAssigning(''); }
  }

  // Give a load back to the date rule. The derived year is recomputed here so
  // the effect is visible immediately rather than at the next import.
  async function releaseCropYear(ids) {
    if (!ids.length) return;
    setAssigning('release'); setLoadErr(null);
    try {
      await patchTickets(ids, { crop_year_locked: false });
      const byYear = {};
      for (const id of ids) {
        const t = cropAllYears.find(x => x.id === id);
        if (!t) continue;
        const derived = grainDeliveryCropYear(t.crop, t.delivery_date);
        if (derived == null || Number(derived) === Number(t.crop_year)) continue;
        (byYear[derived] = byYear[derived] || []).push(id);
      }
      for (const [y, list] of Object.entries(byYear)) await patchTickets(list, { crop_year: Number(y) });
      setSelectedLoads(new Set());
      await loadAll();
    } catch (e) { setLoadErr(String(e.message || e).slice(0, 160)); } finally { setAssigning(''); }
  }

  const elevatorNet = cropTickets.reduce((s, t) => s + (Number(t.net_bushels) || 0), 0);
  // RMA net, delivered, stored/seed/fed and the appraised bushels are NOT added up here —
  // see `hc` below, which hands every source to the one engine (claimEstimate.js).
  const shrinkableLoads = cropTickets.filter(t => isFinite(Number(t.gross_bushels)) && Number(t.gross_bushels) > 0 && isFinite(Number(t.moisture))).length;
  // Silage appraisals (corn): appraised GRAIN bu on chopped acres = production-to-count for
  // those acres (no scale ticket exists). A field appraised but then HARVESTED shows real
  // yield instead — for JD-connected farms that overlap is visible below; otherwise back it
  // out with the include toggle. Only Grain-unit appraisals (bu) add to production; silage
  // ton appraisals are informational.
  const cropSilage = useMemo(() => (silage || []).filter(s => Number(s.crop_year) === Number(year)), [silage, year]);
  // One roll-up rule, shared with tools/check-appraisal-sheet.js. It keeps the
  // units APART: `bu` totals only bushel rows and `buAcres` is the acreage
  // behind them, so a silage tonnage can never be added to a grain
  // production-to-count figure or inflate the acres printed beside it.
  const apprSum = summarizeAppraisals(cropSilage);
  const appraisedBu = apprSum.bu;
  const appraisedAcres = apprSum.buAcres;
  // What John Deere's own per-CLU yield split would say, OFFERED beside each box.
  // ⚠ MACHINE production, not the scale tickets a claim settles on — the two differ for
  // ordinary reasons (moisture basis, an uncalibrated monitor, grain left in the field).
  // Nothing here writes; accepting it is a click, and it stamps source='jd' so a frozen
  // estimate can always say where its number came from. (Declared here, ahead of the
  // engine call, because the engine shows it BESIDE production as a check.)
  const jdCounty = useMemo(() => {
    const ftfAll = (jdData && (jdData.ftf || jdData.yieldFtf)) || [];
    // A field marked high-moisture corn is valued by its appraisal: its combine bushels are not offered.
    const ftf = dropHmcRows(ftfAll, ((acctRead && acctRead.hmcKeys) || []).map((x) => ({ field_key: x })));
    if (!ftf.length) return [];
    return jdCountySuggestion(ftf, { crop: cropU, year });
  }, [jdData, cropU, year, acctRead]);
  const jdAcresCounted = (jdCounty || []).reduce((s, r) => s + (Number(r.acres) || 0), 0);
  const jdEstBu = (jdCounty || []).reduce((s, r) => s + (Number(r.bushels) || 0), 0);
  // ── THE ONE HARVEST ENGINE ─────────────────────────────────────────────────
  // Every production source is handed in BY NAME. The include-appraisals toggle passes or
  // omits that source — it is not a second formula. Machine yield rides beside and is never
  // added. The roster calls the same function with the same keys.
  const hc = computeHarvestClaim(cov, {
    tickets: cropTickets, shrinkPerPt, baseMoisture: baseMoist,
    deliveredOverride: manualDelivered !== '' ? manualDelivered : null,
    stored: storedBu, seed: seedBu, fed: fedBu,
    appraisals: includeSilage && apprSum.bu > 0 ? apprSum : null,
    machine: jdEstBu > 0 ? { bu: jdEstBu, acres: jdAcresCounted } : null,
    // Grain-insured combine bushels, the Claims page's own "Combined" figure (grainCombineOf).
    combine: (acctRead && acctRead.combine) || null,
  }, harvest?.price ?? null);
  const rmaNet = hc.rmaNet;
  const deliveredForPtc = hc.delivered;
  const extra = hc.extra;
  const silageAdd = hc.appraisedBu;
  // Dollar estimates and the ticket-vs-machine flag are ADMIN-only (settled 2026-10-18):
  // this screen also renders for a portal sub-agent through the Claims Manager.
  const canSeeClaimEstimate = isCurrentUserAdmin();
  // Appraisal coverage: what share of the farmer's PLANTED corn acres carry an appraisal,
  // grouped by FSA Farm No (the reliable tie — appraisals carry FSN, not a CLU-matchable field
  // id). Unappraised acres are where production must come from grain harvest (tickets/JD) — the
  // "go looking" signal. Not a double-count check: it's planted-vs-appraised acres, per farm.
  //
  // ⚠ THE DENOMINATOR IS PLANTED, NEVER INSURED, AND THE DIFFERENCE IS PREVENT PLANT.
  // `crop_coverage.acres` is insured = planted + PP (CLAUDE.md, "THERE ARE THREE ACRE
  // FIGURES"), so insured-as-denominator counts acres that were never in the ground and
  // can therefore never carry an appraisal OR a grain ticket. Measured on LANG FARMS LLC
  // CY2026: 97.8 insured corn acres, of which 61.1 are PP across two FSNs that are 100%
  // prevent plant. Against insured the panel read "38% appraised · 61 ac not appraised —
  // expect grain harvest on those"; against planted it reads 36.7 of 36.7 = 100%, which is
  // what the grower's own R&H statement says (36.70 PL). Telling an agent to go looking for
  // grain on 61 prevent-plant acres is a hunt that cannot end.
  //
  // PP acres are REPORTED as their own accounted category rather than dropped: a farm that
  // vanishes from the table reads as a farm nobody checked, and the PP acres are genuinely
  // settled — they are the other half of the account, not a gap in it.
  const fsnCoverage = useMemo(() => {
    if (cropU !== 'CORN') return null;
    // Only farms with an OPEN silage appraisal (a change of intent — grain-insured corn chopped
    // for silage, or the reverse). Most corn growers never get appraised; showing 0% for them is
    // noise. No appraisal on file → no panel.
    if (!cropSilage.length) return null;
    // ⚠ A FARM NAMES EVERY COUNTY ITS INSURED GROUND IS IN, rather than the first one
    // to arrive. An FSA farm is not county-specific — Mulligan's farm 6198 holds 545.0
    // Livingston-insured acres and 236.9 Monroe-insured, so a single label is false of
    // it either way, and the first-wins pick is the shape this file records for
    // type_code, unit_number and the P15 yield key. The county shown is the INSURANCE
    // county (`insured_county`, the coverage's own, from P14) with the FSA one as the
    // fallback for rows written before sql/041.
    const mk = (k, county) => ({ fsn: k, county: county || null, counties: new Set(),
                                 insured: 0, pp: 0, planted: 0, appraised: 0, appraisedBu: 0 });
    const byFsn = {};
    // ⚠ GRAIN ONLY. A silage-insured (026) field is settled in wet tons, never by a bushel
    // appraisal, so its acres are not "planted ac not appraised — expect grain harvest", and
    // a bushel line on 026 is an adjuster error the Claims page flags, not coverage. Counting
    // either here put Verratti's 977.5 silage ac in this grain panel (audit 2026-11-03, #10).
    const isSilageField = (x) => appraisalInsuredType(x) === 'silage';
    (insuredFields || []).forEach(f => {
      if (isSilageField(f)) return;
      const k = (f.fsn || '—').toString();
      const fc = String(f.insured_county || f.county || '').trim().toUpperCase();
      const b = (byFsn[k] = byFsn[k] || mk(k, fc));
      if (fc) b.counties.add(fc);
      const ac = Number(f.acres) || 0;
      // pp_acres is SELECTED by the query above — reading it without asking for it is the
      // dead-guard failure check-query-contract exists for.
      const pp = Math.min(ac, Math.max(0, Number(f.pp_acres) || 0));
      b.insured += ac; b.pp += pp; b.planted += ac - pp;
    });
    cropSilage.forEach(s => {
      if (isSilageField(s)) return;
      // The line's own FSN-TRACT-FIELD id outranks a stored fsn stamped from the sheet
      // header — see fsnTractField(). Stored rows written before that fix carry the
      // header's FSN on every line, so deriving here corrects them without a re-upload.
      const ftf = fsnTractField(s.field_name);
      const k = ((ftf && ftf.fsn) || s.fsn || '—').toString();
      const b = (byFsn[k] = byFsn[k] || mk(k, s.county));
      b.appraised += Number(s.appraised_acres) || 0;
      b.appraisedBu += Number(s.appraised_bu) || 0;
    });
    const rows = Object.values(byFsn)
      .map(r => ({
        ...r,
        // Joined, never picked: two counties on one farm is a fact about the farm.
        county: r.counties.size ? [...r.counties].sort().join(' · ') : (r.county || null),
        countyCount: r.counties.size,
        // A farm that is entirely prevent plant has no planted acres to appraise: pct is
        // null (not 0%), and it can never be "unaccounted" — those acres are settled.
        allPp: r.insured > 0.05 && r.planted <= 0.05,
        pct: r.planted > 0.05 ? r.appraised / r.planted : null,
        unaccounted: Math.max(0, r.planted - r.appraised),
      }))
      .sort((a, b) => (b.unaccounted - a.unaccounted) || (b.planted - a.planted) || (b.insured - a.insured));
    if (!rows.length) return null;
    const insuredTot = rows.reduce((s, r) => s + r.insured, 0);
    const ppTot = rows.reduce((s, r) => s + r.pp, 0);
    const plantedTot = rows.reduce((s, r) => s + r.planted, 0);
    const apprTot = rows.reduce((s, r) => s + r.appraised, 0);
    const fsnsWithAppr = rows.filter(r => r.appraised > 0.05).length;
    const fsnsTot = rows.filter(r => r.planted > 0.05 || r.appraised > 0.05).length;
    return { rows, insuredTot, ppTot, plantedTot, apprTot,
             pct: plantedTot > 0.05 ? apprTot / plantedTot : null,
             unaccountedTot: Math.max(0, plantedTot - apprTot), fsnsWithAppr, fsnsTot };
  }, [insuredFields, cropSilage, cropU]);
  const production = hc.production;

  // ── WHAT IS ATTRIBUTED TO A COUNTY, AND WHAT IS NOT ────────────────────────
  // One rule, imported — the panel never re-derives the arithmetic it prints. The
  // reconciliation is the honest half: a county nobody stated is UNSETTLED, production
  // nobody placed is a named REMAINDER, and more placed than exists is an entry ERROR
  // rather than a remainder clamped to zero.
  // ⚠ ONLY COUNTIES WITH INSURED ACRES GET A PRODUCTION BOX. crop_coverage carries a row per
  // county from the moment a policy is written and RMA keys the acreage later, so a county the
  // grower has no acres in arrives here with acres NULL — and an empty bushels box beside it
  // reads as production somebody forgot to enter. That is how STEUBEN came to sit in
  // BRIDGEWATER FARMING's CY2026 wheat with zero acres, zero liability and zero fields, and
  // then block the report: Kevin, "it says we dont have enough data but I think we do".
  //
  // Measured book-wide CY2025+: 298 such counties across 181 multi-county cells. They are NAMED
  // by the production panel above — never silently dropped — they simply get no box to fill.
  const reportableCounties = useMemo(() => {
    const units = (cov && cov.units) || [];
    const all = (cov && cov.counties) || [];
    const hasAcres = (c) => units.some((u) => String(u.county || "").toUpperCase() === String(c || "").toUpperCase() && +u.acres > 0);
    const keep = all.filter(hasAcres);
    // Fail OPEN: if no unit carries acres at all (an unkeyed book, or a shape this does not
    // know), show every county rather than an empty panel — a missing box cannot be filled in.
    return keep.length ? keep : all;
  }, [cov]);

  const countyRecon = useMemo(() => reconcileCountyProduction({
    counties: reportableCounties,
    entries: Object.keys(countyBu).map((county) => ({
      county, bushels: countyBu[county] === '' ? null : Number(countyBu[county]),
      uom: cropU === 'CORN SILAGE' ? 'ton' : 'bu', source: countySrc[county] || 'manual',
    })),
    totalProduction: production > 0 ? production : null,
    // ⚠ PASSED EXPLICITLY, never left to the default. `check-shared-rules` counts
    // POSITIONAL arguments and this is one options object, so a dropped `unit` KEY is
    // invisible to it — the same shape as the dropped `bfr` key on the wheat quoter.
    // Omitted, every potato county reads "cwt" on screen and "bushels" in the sentence
    // beneath it.
    unit: cropUnit,
  }), [reportableCounties, countyBu, countySrc, production, cropU, cropUnit]);

  // ── APPRAISED PRODUCTION, PLACED IN A COUNTY ───────────────────────────────
  // Kevin, 2026-10-06: "Production by county says not stated but it's literally in the
  // appraisal." He is right, and the contradiction was on one screen: the panel asked for
  // a number it was already rendering twenty lines below. An appraised GRAIN figure is
  // production-to-count for chopped acres — no scale ticket exists for them.
  //
  // ⚠ THE COUNTY COMES FROM THE INSURED FIELD MAP VIA FSN, NEVER FROM THE APPRAISAL ROW,
  // and that distinction is worth 18,923 bu on the first farm it was tried on. Measured
  // 2026-10-06, MULLIGAN FAMILY FARM LLC CY2026: all 61 appraisal rows store
  // `county = LIVINGSTON`, while FSN 6116 (90 ac) and 6121 (5 ac) carry MONROE in the
  // insured field map. The stored county is stamped from the SHEET HEADER — the same
  // defect this component already corrects for `fsn` — so trusting it would have placed
  // Monroe's production in Livingston on a claim settlement, which is the one direction
  // that moves a grower's indemnity.
  //
  // FSN -> county is unambiguous in this book: 0 of 2,310 (farmer, FSN) CY2026 corn pairs
  // span two counties. That is a MEASUREMENT, not a guarantee — an FSN that does span two
  // is left UNPLACED and NAMED, never split by a guess, and so is one with no insured
  // field at all. Those are different reasons and the panel says which.
  //
  // ⚠ ONE RULE, TWO CALLERS — `appraisalsByCounty` is the SAME join ProductionEvidencePanel
  // uses, extracted rather than re-written here. The first cut of this panel grew its own
  // copy and it was already drifting: it refused an ambiguous farm where the shared rule
  // keeps the header county and COUNTS it, so the two screens would have disagreed about
  // the same farm. This file's own record is what that costs — three news handlers drifted
  // twice, two guarantee engines disagreed about one farm's forward-sell ceiling.
  //
  // `basis` is the provenance and it is rendered, not swallowed: fromField is a county the
  // insured field map placed, fromHeader is one that kept the worksheet's own county, and
  // `moved` is how many the join re-filed. A rollup that silently re-files acres is
  // indistinguishable from the defect it replaces.
  const apprCounty = useMemo(() => {
    if (!cropSilage.length) return { rows: [], basis: null };
    // ⚠ GRAIN LINES ONLY. A bushel line on SILAGE-insured acres (type 026) is not grain production
    // and, per Kevin (2026-11-03), not a valid silage figure either: it stays out of this per-county
    // "appraisal on file" offer and its "use" button, exactly as productionEvidence leaves it out
    // (HARKINS Wyoming offered 51,647 bu here, every bushel of it on silage-insured acres).
    const { byCounty, basis } = appraisalsByCounty({
      appraisals: cropSilage.filter((s) => appraisalInsuredType(s) !== 'silage'), policyFields: insuredFields || [],
      // ⚠ THE CROP YEAR, because a unit number is reused across years and its county can
      // change — Mulligan's unit 2|1 is Livingston in 2025 and Monroe in 2026, and pooled
      // it resolves to neither. The insuredFields read is year-scoped by its own query, but it
      // query has an UNSCOPED FALLBACK for a year not yet backfilled, so the rule must be
      // told rather than trusting the caller to have narrowed it.
      cropYear: year,
    });
    const rows = [...byCounty.values()]
      .map((v) => ({ county: v.county, bushels: Math.round(v.qty), acres: v.acres, n: v.lines }))
      .filter((r) => r.bushels > 0)
      .sort((a, b) => b.bushels - a.bushels);
    return { rows, basis };
  }, [cropSilage, insuredFields, year]);
  // Scaled yield per acre = total production ÷ PLANTED acres. Acre-weighted APH from the
  // units gives the approved-yield baseline to read it against (are they above/below APH).
  //
  // ⚠ PLANTED, NOT INSURED — see summarizeClaimCoverage. Insured includes prevent plant,
  // and no bushel was ever grown on a prevent-planted acre, so dividing by it understates
  // the yield by the PP share. On LANG FARMS' CY2026 corn (72.7 insured = 36.7 planted +
  // 36.0 PP) that printed 67.6 bu/ac and "-33.4 vs APH 101" on a farm actually running
  // 133.9 bu/ac, +32.9 OVER his APH — the sign of the claim signal, inverted, on the
  // screen that decides whether to open one.
  //
  // A farm with NO prevent plant is unaffected: planted == insured, and every number on
  // this panel is identical to what it printed before.
  const insuredAcres = cov?.acres || 0;
  const plantedAcres = cov?.plantedAcres != null ? cov.plantedAcres : insuredAcres;
  const ppAcresCov = cov?.ppAcres || 0;
  // ⚠ GRAIN BUSHELS OVER GRAIN ACRES. plantedAcres also holds silage-insured (026) acres,
  // whose production is tons; dividing bushels by them understates the yield, and a silage
  // unit's ton APH (~19 t/ac) dragged the bushel APH beside it down the same way.
  const grainPlantedAc = cov?.grainPlantedAcres != null ? cov.grainPlantedAcres : plantedAcres;
  const yieldPerAc = grainPlantedAc > 0 && production > 0 ? production / grainPlantedAc : null;
  const aphWtd = useMemo(() => {
    const us = (cov?.units || []).filter(u => u.aph > 0 && u.acres > 0 && u.unit !== 'ton');
    if (!us.length) return null;
    const num = us.reduce((s, u) => s + u.aph * u.acres, 0), den = us.reduce((s, u) => s + u.acres, 0);
    return den > 0 ? num / den : null;
  }, [cov]);

  // ── Guarantee + claim math ────────────────────────────────────────────────
  const guarBu = cov?.guaranteeBu || 0;
  const projected = cov?.projectedPrice || 0;
  const harvestPx = harvest?.price ?? null;
  // ⚠ THE PRICE AND SHORTFALL RULE IS THE ENGINE'S, NOT THIS SCREEN'S. This block used to
  // re-derive the 200% cap, the RP higher-of and the indemnity inline — a second copy of
  // computeHarvestClaim that had already drifted from it (the roster's yield test missed the
  // APH plan; this one did not). The yield test is claimBasis/coverageBasis, inside the
  // engine, so APH, YP and the potatoes/dry-beans/onions books all read yield on both screens.
  const isHPE = hc.isHPE;
  const isYP = hc.isYP;
  const hp = hc.harvestUsed;
  const guarPrice = hc.guarPrice;
  const revGuar = hc.revGuar;
  const revToCount = hc.revToCount;
  const buShort = hc.buShort;
  const revShort = hc.revShort;
  const estIndemnity = hc.estIndemnity;
  const hasData = guarBu > 0 && production > 0;

  // ── PRODUCTION THAT COVERS PART OF THE FARM CANNOT READ A CLAIM ────────────
  // ⚠ AND READING ONE ANYWAY INVERTS ITS SIGN. `production` is subtracted from the WHOLE
  // farm's guarantee, so every planted acre with no figure behind it is silently valued at
  // ZERO bushels.
  //
  // ROGERS FARMS LLC, CY2026 corn, read off the live screen 2026-10-07: 308 appraised acres
  // carrying 60,438 bu — that is 196.2 bu/ac against a 183.0 APH, SEVEN PERCENT ABOVE it —
  // spread across all 1,108 planted acres reads 54.5 bu/ac, "−128.5 vs APH", "Likely claim —
  // open it", and an EST. INDEMNITY of $459,938. If the unappraised ground runs like the
  // ground we have actually measured he is 65,235 bu OVER his guarantee with no claim at all.
  //
  // This is the LANG FARMS inversion recorded against `yieldPerAc` above, arriving through
  // the NUMERATOR instead of the denominator: there prevent-plant acres were wrongly in the
  // divisor, here unharvested acres are wrongly counted as zero bushels in the dividend. Same
  // sign error, same screen, same consequence — the one that decides whether to open a claim.
  //
  // ⚠ THE ANSWER IS "NOT YET", NEVER "NO CLAIM". Flipping the verdict green would be this
  // same mistake with the opposite sign, and a farm genuinely short would be told it is fine.
  // The read is WITHHELD and what is missing is NAMED.
  //
  // ⚠ A WHOLE-FARM FIGURE IS NOT EVIDENCE OF MISSING ACRES. Tickets, delivered, stored, saved
  // seed and fed bushels carry no acreage at all — only an acre-scoped source (an appraisal,
  // a machine pass) can say how much ground it speaks for. So partiality is asserted ONLY
  // where acre-scoped production is the whole of what has been counted; the moment a
  // whole-farm figure exists, nothing here can claim the farm is short of coverage.
  // Insured acres PER COUNTY, so an appraised figure can be read against the ground it is
  // being placed on. Kevin, 2026-10-07: *"it needs to clearly show: This many acres were
  // appraised of this many insured acres and this is the total production thus far (even
  // though we are expecting a lot more production)"*. Without the second number the offer
  // says "60,438 bu over 308 ac" beside a county carrying 1,108 insured acres, and the
  // reader has to hold the comparison in their head — which is how 308 acres of production
  // comes to be accepted as a county's whole answer.
  const insuredByCounty = useMemo(() => {
    const m = new Map();
    for (const u of (cov?.units || [])) {
      const c = String(u.county || '').toUpperCase();
      if (!c) continue;
      m.set(c, (m.get(c) || 0) + (Number(u.acres) || 0));
    }
    return m;
  }, [cov]);

  // ⚠ PLANTED PER COUNTY, from the SAME units insuredByCounty sums, and reusing the per-unit
  // `plantedAcres` the guarantee table already renders rather than subtracting PP a second
  // time. `crop_coverage.acres` is INSURED = planted + PP (CLAUDE.md, "THERE ARE THREE ACRE
  // FIGURES"), so a production surface measuring against insured reports a county's prevent
  // plant as missing production — 27.7 ac on MULLIGAN's LIVINGSTON, printed as "28 ac not
  // appraised — expect more production from those". Nothing grew there; that hunt cannot end.
  const plantedByCounty = useMemo(() => {
    const m = new Map();
    for (const u of (cov?.units || [])) {
      const c = String(u.county || '').toUpperCase();
      if (!c) continue;
      const pl = u.plantedAcres != null ? Number(u.plantedAcres) : Number(u.acres);
      if (!isFinite(pl)) continue;
      m.set(c, (m.get(c) || 0) + Math.max(0, pl));
    }
    return m;
  }, [cov]);

  // ⚠ THE PARTIAL-PRODUCTION RULE IS THE ENGINE'S (claimEstimate.js), so the roster withholds
  // the same read. Only COUNTED acre-scoped sources (appraisals) say how much ground is covered:
  // machine acres are not production, so they can no longer make appraisal-only bushels read
  // as the whole farm.
  const acreScopedAc = hc.acreScopedAcres;
  const prodPartial = hc.partial;
  const uncoveredAc = hc.uncoveredAcres;

  // ── REVENUE GUARANTEE vs ACTUAL REVENUE, WHERE THE MONITOR IS ALL WE HAVE ──
  // Kevin, 2026-10-17, on PANEK, ALAN: "especially now that he's done it should show
  // revenue guarantee vs actual revenue (yield x harvest price)."
  //
  // The revenue test below already does exactly that — revToCount = production x hp —
  // and it is hidden, correctly, because hasData requires production > 0 and nothing
  // has been entered. Printing "$0 actual revenue, $56,420 short" off an empty form
  // would be alarming and wrong.
  //
  // But the harvest IS done and we can see it: measured 2026-10-17 his soybeans are
  // 3/3 fields, 122.4 of 122.2 insured acres, 61.6 bu/ac = 7,541 bu, nothing missing.
  // That number sat in its own collapsed card and fed nothing.
  //
  // ⚠ IT DOES NOT TOUCH `production`. That is production TO COUNT — RMA's own
  // delivered + stored + fed + saved seed — and a monitor figure is none of those
  // (moisture basis, an uncalibrated monitor, grain left in the field). Writing it
  // there would conflate an estimate with a settled quantity on the one screen where
  // that distinction decides money. It is a SEPARATE, labelled read that sets no
  // verdict: hasClaim stays null, so nothing downstream reads a claim off a monitor.
  const jdEstRevenue = (production <= 0 && hp != null && jdEstBu > 0) ? jdEstBu * hp : null;
  // Printed, never gated on. A floor here would be a second invented threshold; the
  // reader can see 60% coverage and judge it, where a hidden card tells them nothing.
  const jdEstCoverage = (plantedAcres > 0 && jdAcresCounted > 0) ? jdAcresCounted / plantedAcres : null;

  const hasClaim = (!hasData || prodPartial)
    ? null
    : (revShort != null ? revShort > 0 : (buShort != null && buShort > 0));

  const card = { background: '#fff', border: '1px solid #e4ece4', borderRadius: 10, padding: isMobile ? 14 : 18 };
  const lbl = { fontSize: 10.5, color: '#60735f', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 };
  const inStyle = { width: '100%', padding: '8px 10px', border: '1px solid #cdddcd', borderRadius: 7, fontSize: 14, fontVariantNumeric: "tabular-nums", boxSizing: 'border-box' };
  const numField = (label, val, set, ph) => (
    <div>
      <div style={lbl}>{label}</div>
      <input type="number" value={val} onChange={e => set(e.target.value)} placeholder={ph || '0'} style={inStyle} />
    </div>
  );

  return (
    <div>
      {!locked && (
      <div style={{ fontSize: 12.5, color: '#60735f', margin: '-4px 0 16px', lineHeight: 1.5 }}>
        Enter or import total production for a farm and crop, and check it against the <strong style={{ color: '#1a2e1a' }}>production</strong> guarantee — and, on a revenue plan, the <strong style={{ color: '#1a2e1a' }}>revenue</strong> guarantee too — a quick "do we need to open a claim?" read. Every crop the farm insures is offered, in that crop's own unit; a yield plan (APH/YP) has no revenue guarantee and says so. Whole-farm per crop; optional-unit losses can hide inside a whole-farm surplus, so treat it as a screener, not the adjuster's final settlement.
      </div>
      )}

      {/* Selectors — hidden when embedded as a Claims Manager drill-in (campaign sets them) */}
      {!locked && (
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ flex: isMobile ? '1 1 100%' : '2 1 260px' }}>
          <div style={lbl}>Farm</div>
          <select value={farmerId} onChange={e => setFarmerId(e.target.value)} style={{ ...inStyle, fontFamily: 'inherit' }}>
            <option value="">Select a farm…</option>
            {sortedFarmers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 130px' }}>
          <div style={lbl}>Crop</div>
          <select value={crop} onChange={e => setCrop(e.target.value)} style={{ ...inStyle, fontFamily: 'inherit' }}>
            {cropChoices.map(c => <option key={c.crop} value={c.label}>{c.label}{c.acres ? ` · ${Math.round(c.acres).toLocaleString()} ac` : ''}</option>)}
            {/* The selected crop may not be one this farm insures — the picker keeps the
                previous selection when the farm changes. Offer it rather than silently
                snapping to another crop, so "no coverage" is a readable answer. */}
            {!cropChoices.some(c => c.crop === cropU) && <option value={crop}>{crop} — no coverage on this farm</option>}
          </select>
        </div>
        <div style={{ flex: '1 1 100px' }}>
          <div style={lbl}>Crop year</div>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inStyle, fontFamily: 'inherit' }}>
            {[CURRENT_CROP_YEAR + 1, CURRENT_CROP_YEAR, CURRENT_CROP_YEAR - 1, CURRENT_CROP_YEAR - 2].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      )}

      {!farmerId ? (
        <Card style={{ textAlign: 'center', padding: '40px 24px', border: '1px dashed #c8d8c8' }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>🧮</div>
          <div style={{ fontWeight: 800, color: '#1a2e1a', marginBottom: 6 }}>Pick a farm to check</div>
          <div style={{ color: '#60735f', fontSize: 13 }}>Choose a farm and crop above, then enter or import harvested bushels.</div>
        </Card>
      ) : loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#60735f' }}><UiSpinner />Loading coverage & tickets…</div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14 }}>
      {/* ⚠ minmax(0, 1fr), NOT 1fr — AND THIS IS THE HALF THAT WAS MISSING.
          A bare `1fr` is `minmax(AUTO, 1fr)`, and that auto minimum is on the TRACK.
          The track therefore grows to its widest content and the item overflows the
          grid box, whatever min-width the ITEM carries — so the `minWidth: 0` on the
          wrapper below, added for exactly this symptom, could never work on its own.

          MEASURED at a 390px viewport, 2026-10-06: grid box 342, track 417, and every
          value in the card ending at 428. That is the whole of the cut-off right edge
          Kevin photographed — "WHERE THIS CLAIM STANDS" with no Guarantee and no
          Production figure on screen at all.

          Found by walking the ancestor chain and reading the widths. Three plausible
          causes were eliminated first and each would have been a wrong fix: the modal
          panel's flex min-width, the card's own min-width, and the app's 390px mobile
          PREVIEW frame (which is desktop-only and was not even rendering). A layout
          bug guessed at is a layout bug moved. */}

        {/* ⚠ THE READ LEADS, AND THE PANEL OWNS IT. It needs three things this component has
            (the guarantee, the plan and whether a claim is indicated) and three it does not
            (which channel the production came from, that channel's RMA code, and the acres a
            combine actually covered). Passing the claim read DOWN is the smaller seam: the
            arithmetic stays in one place, and a second copy of it is how a headline comes to
            disagree with the card below it. It spans both columns because it is the answer,
            not one of a pair of details. */}
        {/* ⚠ `minWidth: 0` BELOW IS LOAD-BEARING. A grid item defaults to `min-width: auto`,
            which means "never shrink below your widest content" — so the panel's eight-column
            field table blew the single mobile track out to 603px inside a 390px modal and the
            right-hand values were CLIPPED, not scrollable: the guarantee, the production and
            the third acres column simply were not on screen. It did not happen before because
            this panel used to render OUTSIDE the grid, and the modal's own overflow detector
            reports clean either way, because the clipping is inside the modal rather than on
            the page — only a screenshot showed it. */}
        {/* ═══ STAGE 1 ═══ */}
        <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1', margin: '2px 0 -4px',
                      display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                         width: 21, height: 21, borderRadius: 11, background: '#2d4a2d',
                         color: '#fff', fontSize: 11.5, fontWeight: 800, flex: '0 0 auto' }}>1</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#1a2e1a',
                         letterSpacing: '.02em' }}>ACRES INSURED</span>
          <span style={{ fontSize: 11.5, color: '#60735f' }}>how many acres, in which counties, and are they all spoken for</span>
        </div>

        {selFarmer && (
          <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1', minWidth: 0 }}>
            <ProductionEvidencePanel
              farmer={selFarmer} crop={crop} cropYear={year} isMobile={isMobile} onAccounting={setAcctRead}
              claimRead={{
                guaranteeBu: guarBu || null,
                // ⚠ A ROLLUP OF THE RULE THAT IS ALREADY THERE, NEVER A SECOND COPY OF IT.
                // Kevin, 2026-10-07: "add a bushel guarantee and a dollar guarantee to this
                // table, then it would all be in one place." summarizeClaimCoverage already
                // emits one row per coverage line with its own county, buGuar and liability —
                // liability-first through the shared guaranteeBushels(), which is the SOI's
                // Marketable Guarantee. Grouping those is a VIEW of that answer; deriving a
                // county guarantee inside ProductionEvidencePanel would be the second engine
                // this file keeps recording the cost of.
                //
                // ⚠ TONS AND BUSHELS STAY APART. A corn cell can hold a GSG grain line and a
                // GSS silage line, and `unit` on each unit row says which. Summing them is the
                // conflation that printed Lismore's Steuben corn at 86.5.
                guaranteeByCounty: (() => {
                  const m = {};
                  for (const u of (cov?.units || [])) {
                    const k = String(u.county || '').trim().toUpperCase() || '(county not stated)';
                    const e = (m[k] ||= { bu: 0, ton: 0, liability: 0, plantedAcres: 0,
                                         guaranteedAcres: 0, unpricedLines: 0 });
                    if (u.unit === 'ton') e.ton += u.buGuar || 0; else e.bu += u.buGuar || 0;
                    // The RP-revalued revenue guarantee, from the SAME bushel guarantee and
                    // the SAME price the table below this card prints — not RMA's stored
                    // liability, which is struck at the projected price and disagreed with
                    // the table by $80,984 on MULLIGAN.
                    e.liability += (u.buGuar != null && guarPrice != null) ? u.buGuar * guarPrice : (u.liability || 0);
                    e.liabilityIsStored = e.liabilityIsStored || !(u.buGuar != null && guarPrice != null);
                    e.plantedAcres += u.plantedAcres || 0;
                    // ⚠ COMPLETENESS IS WHETHER THE PRICED LINES COVER THE COUNTY'S ACRES,
                    // NOT A COUNT OF ROWS THAT DID NOT PRICE. The first cut counted every
                    // `via !== 'ok'` line and withheld the guarantee on both of MULLIGAN's
                    // counties — because his coverage carries two ZERO-ACRE silage lines
                    // (APH 19.5 ton/ac, 0 acres, 0 liability). A row with no acres and no
                    // liability is not an unpriced line, it is an EMPTY one, and it says
                    // nothing about whether the county's real acreage is priced.
                    //
                    // `u.acres` is only set where the line produced a guarantee, so summing
                    // it gives exactly the acreage the figure above covers. The caller
                    // compares that against the county's own insured acres.
                    e.guaranteedAcres += u.acres || 0;
                    if (u.via !== 'ok') e.unpricedLines++;
                  }
                  return m;
                })(),
                hasClaim,
                // The ONE production figure the card prints (headline, % and verdict), and what is in it.
                production: hc.production,
                measuredBu: (hc.delivered || 0) + (hc.extra || 0),
                combineCounted: hc.combineCounted,
                appraisalsCounted: includeSilage,
                // Why the read is withheld, so the card can say "not yet" with the acres
                // rather than falling back to "enter production" on a farm where production
                // HAS been entered — it just does not cover the ground.
                partial: prodPartial
                  ? { uncovered: Math.round(uncoveredAc), planted: Math.round(plantedAcres),
                      covered: Math.round(acreScopedAc) }
                  : null,
                planLabel: [(cov?.plans || []).join('/') || null, harvestPx == null ? null : `${px(guarPrice)} guar.`]
                  .filter(Boolean).join(' · ') || null,
              }}
            />
          </div>
        )}


        {/* ═══ STAGE 2 ═══ */}
        <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1', margin: '2px 0 -4px',
                      display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                         width: 21, height: 21, borderRadius: 11, background: '#2d4a2d',
                         color: '#fff', fontSize: 11.5, fontWeight: 800, flex: '0 0 auto' }}>2</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#1a2e1a',
                         letterSpacing: '.02em' }}>PRODUCTION</span>
          <span style={{ fontSize: 11.5, color: '#60735f' }}>what is accounted for, by what source, and what is missing</span>
        </div>

        {/* ── PRODUCTION TO COUNT ── */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 800, color: '#1a2e1a', fontSize: 15 }}>Production to count</div>
            <label style={{ fontSize: 11.5, fontWeight: 700, color: '#2d6a2d', cursor: 'pointer', border: '1px solid #cfe6cf', borderRadius: 7, padding: '5px 10px', background: '#f2f9f2' }}>
              🎫 Import CSV
              <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) onImport(f); e.target.value = ''; }} />
            </label>
          </div>
          {imp && (
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 10, padding: '7px 10px', borderRadius: 6, lineHeight: 1.4,
              background: imp.status === 'err' ? '#fbeae5' : imp.status === 'ok' ? '#e8f6ec' : '#f2f6f2',
              color: imp.status === 'err' ? '#b3300f' : imp.status === 'ok' ? '#2d6a2d' : '#4a5a4a' }}>
              {imp.status === 'busy' ? '⏳ ' : imp.status === 'ok' ? '✅ ' : imp.status === 'err' ? '⚠ ' : ''}{imp.msg}
            </div>
          )}

          {uploadBatches.length > 0 && (
            <div style={{ marginBottom: 10, padding: '8px 10px', background: '#f6f9f6', borderRadius: 7, border: '1px solid #e4ece4' }}>
              <div style={{ ...lbl, marginBottom: 6 }}>Uploads ({uploadBatches.length}){uploadBatches.length > 1 ? ' — delete the wrong CSV' : ''}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {uploadBatches.map(b => (
                  <div key={b.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 11.5 }}>
                    <span style={{ color: '#4a5a4a' }}>{fmtUploadTime(b.imported_at)} · <strong>{b.n}</strong> load{b.n > 1 ? 's' : ''} · <strong style={{ fontVariantNumeric: "tabular-nums" }}>{bu(b.bu)}</strong> bu{b.buyer ? ` · ${b.buyer}` : ''}</span>
                    <button onClick={() => deleteBatch(b)} title="Delete this upload's scale tickets" style={{ border: '1px solid #e5b8ac', background: '#fdf2ef', color: '#b3300f', borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>✕ Delete</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <details open={cropTickets.length > 0 || countyRecon.rows.length === 0 || [manualDelivered, storedBu, seedBu, fedBu].some((v) => Number(v) > 0)}
                   style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#2d4a2d', padding: '4px 0' }}>
              Whole-farm figures — scale tickets, delivered, stored, seed, fed
            </summary>
          {cropTickets.length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
                <div style={{ background: '#f6f9f6', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={lbl}>Elevator net</div>
                  <div style={{ fontSize: 17, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#4a5a4a' }}>{bu(elevatorNet)}</div>
                  <div style={{ fontSize: 10, color: '#60735f' }}>bu paid basis (post-dock)</div>
                </div>
                <div style={{ background: '#eef6ef', borderRadius: 8, padding: '8px 10px', border: '1px solid #cfe6cf' }}>
                  <div style={lbl}>RMA net</div>
                  <div style={{ fontSize: 17, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{bu(rmaNet)}</div>
                  <div style={{ fontSize: 10, color: '#60735f' }}>{shrinkPerPt}%/pt shrink to {baseMoist}%</div>
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: rmaNet >= elevatorNet ? '#2d6a2d' : '#b34a10', marginBottom: 8 }}>
                Δ {rmaNet >= elevatorNet ? '+' : ''}{bu(rmaNet - elevatorNet)} bu &mdash; RMA counts {rmaNet >= elevatorNet ? 'more' : 'fewer'} bushels (elevator dock also includes handling/drying shrink). {cropTickets.length} load{cropTickets.length > 1 ? 's' : ''}{buyers.length > 1 ? ` · ${buyers.length} elevators` : ''}{shrinkableLoads < cropTickets.length ? ` · ${cropTickets.length - shrinkableLoads} missing gross/moisture (used net)` : ''}.
              </div>
              {buyers.length > 1 && (
                <div style={{ fontSize: 10.5, color: '#60735f', marginTop: -4, marginBottom: 8 }}>Elevators: {buyers.join(', ')}</div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={lbl}>RMA shrink %/pt</div>
                  <input type="number" step="0.1" value={shrinkPerPt} onChange={e => setShrinkPerPt(Number(e.target.value) || 0)} style={inStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={lbl}>Base moisture %</div>
                  <input type="number" step="0.1" value={baseMoist} onChange={e => setBaseMoist(Number(e.target.value) || 0)} style={inStyle} />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#60735f', marginBottom: 12, lineHeight: 1.5 }}>
              No {crop.toLowerCase()} scale tickets on file for {year}. Import a Bushel CSV above, or enter delivered {cropUnit} manually below.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr', gap: 10 }}>
            {numField(cropTickets.length ? 'Delivered (override RMA net)' : `Delivered ${cropUnit}`, manualDelivered, setManualDelivered, cropTickets.length ? bu(rmaNet) : '0')}
            {numField(`On-farm stored ${cropUnit}`, storedBu, setStoredBu)}
            {numField(`Saved seed ${cropUnit}`, seedBu, setSeedBu)}
            {numField(`Fed / other ${cropUnit}`, fedBu, setFedBu)}
          </div>
          </details>

          {/* ══════════ BUSHELS BY COUNTY (Kevin, 2026-09-28) ══════════
              "Make sure you allow us a spot to manually enter bushels by county in the
              claims manager tool."
              ⚠ A CLAIM SETTLES PER UNIT AND A SCALE TICKET CANNOT SAY WHERE THE BUSHELS
              CAME FROM — all 1,907 on file carry the ELEVATOR's location, not the farm's
              county, and a grower hauling two counties to one elevator produces tickets
              that are indistinguishable. So it is typed here, or accepted from the
              machine, and never derived. */}
          {countyRecon.rows.length > 0 && (
            <div style={{ marginTop: 12, borderTop: '1px dashed #e4ece4', paddingTop: 10 }}>
              <div style={{ fontWeight: 700, color: '#1a2e1a', fontSize: 13, marginBottom: 2 }}>
                {/* ⚠ A COUNTY WITH NO INSURED ACRES GETS NO BOX. Offering one invites a figure
                    for ground the grower does not farm, and a blank box reads as production
                    somebody forgot — which is exactly how STEUBEN came to block Bridgewater's
                    report. It is named once, above, as having no acreage keyed. */}
                📍 Production by county
                <span style={{ fontWeight: 400, color: '#60735f', fontSize: 11 }}>
                  {' '}— a claim settles per unit, and a scale ticket carries the elevator's
                  location, not the farm's county
                </span>
              </div>
              {/* ⚠ THE STATUS IS THE HONEST HALF. Unsettled counties, an unplaced
                  remainder and an over-entry are three different answers and each is
                  said in words — a "—" in a bushels column is exactly the thing a
                  reader fills in with zero. */}
              <div style={{ fontSize: 11.5, fontWeight: 600, margin: '6px 0 8px', padding: '6px 10px',
                            borderRadius: 6,
                            background: countyRecon.status.kind === 'complete' ? '#e8f6ec'
                                      : countyRecon.status.kind === 'over' ? '#fbeae5' : '#fdf7ea',
                            color: countyRecon.status.kind === 'complete' ? '#2d6a2d'
                                 : countyRecon.status.kind === 'over' ? '#b3300f' : '#7a5c00' }}>
                {countyRecon.status.kind === 'complete' ? '✅ ' : countyRecon.status.kind === 'over' ? '⚠ ' : 'ⓘ '}
                {countyRecon.status.text}
              </div>
              {/* Kevin, 2026-10-06: "Show me a progress bar or acres appraised maybe?
                  Seems like it should be straight forwards but right now it's way too
                  over complicated." This is the "am I done" question, answered once, at
                  the top — the appraisal-coverage bar already existed but sits below the
                  appraisal table, which is most of a phone screen away.

                  ⚠ THE BAR COUNTS COUNTIES SETTLED, NOT BUSHELS PLACED, and the two
                  disagree in the direction that matters. A claim settles PER UNIT, so a
                  farm whose largest county carries 95% of the bushels would show a 95%
                  bushel bar while the claim still cannot be filed. Bushels placed are
                  reported beside it as a figure, never as the progress. */}
              {countyRecon.rows.length > 0 && (() => {
                const tot = countyRecon.rows.length;
                const done = countyRecon.rows.filter((r) => r.stated).length;
                const pc = done / tot;
                const col = pc >= 1 ? '#2d6a2d' : pc > 0 ? '#b8860b' : '#b34a10';
                return (
                  <div style={{ margin: '0 0 10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                                  flexWrap: 'wrap', gap: 6, fontSize: 11.5, marginBottom: 4 }}>
                      <span style={{ fontWeight: 800, color: col }}>
                        {done} of {tot} {tot === 1 ? 'county' : 'counties'} stated
                      </span>
                      {countyRecon.total != null && (
                        <span style={{ color: '#60735f' }}>
                          {bu(countyRecon.attributed)} of {bu(countyRecon.total)} bu placed
                        </span>
                      )}
                    </div>
                    <div style={{ height: 8, background: '#eef3ee', borderRadius: 5, overflow: 'hidden' }}>
                      <div style={{ width: `${pc * 100}%`, height: '100%', background: col, transition: 'width .3s' }} />
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                {countyRecon.rows.map((r) => {
                  const sug = jdCounty.find((x) => x.county === r.county);
                  const asug = apprCounty.rows.find((x) => x.county === r.county);
                  return (
                    <div key={r.county}>
                      <div style={lbl}>
                        {r.county}
                        {r.source === 'jd' && <span style={{ color: '#2d6a2d', fontWeight: 700 }}> · from John Deere</span>}
                        {r.source === 'appraisal' && <span style={{ color: '#7a5c00', fontWeight: 700 }}> · from the appraisal</span>}
                      </div>
                      <input
                        type="number" inputMode="decimal"
                        value={countyBu[r.county] == null ? '' : countyBu[r.county]}
                        onChange={(e) => {
                          const v = e.target.value;
                          // '' stays '' — see the state note. Typing clears any JD stamp,
                          // because the number is now a human's.
                          setCountyBu((m) => ({ ...m, [r.county]: v }));
                          setCountySrc((m) => ({ ...m, [r.county]: 'manual' }));
                        }}
                        placeholder="not stated"
                        style={inStyle}
                      />
                      {/* ⚠ OFFERED, NEVER FILLED IN. Machine production is not the scale
                          tickets a claim settles on; accepting it is a deliberate click
                          and it records that the figure came from the monitor. */}
                      {sug && (
                        <div style={{ fontSize: 10.5, color: '#5a6a5a', marginTop: 3 }}>
                          John Deere: <b>{bu(sug.bushels)}</b> over {sug.acres} ac
                          {' '}
                          <button
                            onClick={() => {
                              setCountyBu((m) => ({ ...m, [r.county]: String(sug.bushels) }));
                              setCountySrc((m) => ({ ...m, [r.county]: 'jd' }));
                            }}
                            style={{ cursor: 'pointer', border: '1px solid #cfe6cf', borderRadius: 5,
                                     padding: '1px 6px', fontSize: 10, fontWeight: 700,
                                     background: '#f2f9f2', color: '#2d6a2d' }}>
                            use
                          </button>
                        </div>
                      )}
                      {/* ⚠ OFFERED, NEVER FILLED IN — the same rule as the John Deere line
                          above, for the same reason: a claim settles on a figure a human
                          entered, and an appraisal is an estimate of standing corn, not a
                          weight ticket. What changes is that the number is now ON the page
                          instead of sitting in a table below it saying "not stated". */}
                      {asug && (
                        <div style={{ fontSize: 10.5, color: '#5a6a5a', marginTop: 3 }}>
                          {/* ⚠ THE DENOMINATOR IS THE POINT. "60,438 bu over 308 ac" beside a
                              county carrying 1,108 insured acres leaves the reader holding the
                              comparison in their head, and accepting it places a THIRD of the
                              county's ground as the county's whole answer. Both acre figures
                              are stated, and where the appraisal covers materially less than
                              the county the shortfall is named with the words Kevin used —
                              more production is expected, this is the total THUS FAR. */}
                          {/* ⚠ AND THE DENOMINATOR IS PLANTED, FOR THE SAME REASON THE LINE
                              BELOW IT IS. This read "1155.5 of 1,183 insured ac" — a 28-acre
                              gap that is entirely MULLIGAN's prevent plant, stated as a
                              shortfall in the one place a reader checks whether the adjuster
                              covered the county. Removing the warning sentence and leaving
                              this label would have fixed the sentence and kept the
                              misreading. The insured figure is still shown, after the
                              prevent plant that explains the difference. */}
                          {(() => {
                            // The acreage in this hint is the by-county table's own line (claimAccountingView),
                            // not a second subtraction: accounted = planted less "nothing yet".
                            const L = acctRead && acctRead.mode === 'county' && (acctRead.lines || []).find((x) => x.county === String(r.county).toUpperCase());
                            if (!L) return <>Appraisal on file: </>;
                            return <>Accounted for: <b>{bu(L.planted - L.nothing)}</b> of <b>{bu(L.planted)}</b> planted ac
                              {L.nothing > 0.05 ? <> · <b>{bu(L.nothing)}</b> ac nothing yet</> : null} · appraisal on file: </>;
                          })()}
                          <b>{bu(asug.bushels)}</b> bu
                          {' '}
                          <button
                            onClick={() => {
                              setCountyBu((m) => ({ ...m, [r.county]: String(asug.bushels) }));
                              setCountySrc((m) => ({ ...m, [r.county]: 'appraisal' }));
                            }}
                            style={{ cursor: 'pointer', border: '1px solid #e6dcc0', borderRadius: 5,
                                     padding: '1px 6px', fontSize: 10, fontWeight: 700,
                                     background: '#fbf7ec', color: '#7a5c00' }}>
                            use
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* An entry for a county this farm has no coverage in — a typo, or ground we
                  did not know they farm. Shown, because dropping it would also make the
                  attributed total disagree with the boxes above. */}
              {countyRecon.orphans.length > 0 && (
                <div style={{ fontSize: 11, color: '#7a5c00', marginTop: 8 }}>
                  ⚠ {countyRecon.orphans.map((o) => `${o.county} ${bu(o.bushels)}`).join(' · ')}
                  {' '}— no coverage for this crop there
                </div>
              )}
              {/* WHERE THE APPRAISED COUNTY CAME FROM, said rather than implied, and NAMED
                  BY RUNG. The unit is the strong answer — an RMA unit sits in one county, so
                  a line carrying one needs no inference. The FSA farm is the weaker one: a
                  farm number can hold insured ground in two counties, which is the ordinary
                  case rather than an oddity. The header is the last resort, kept and COUNTED
                  rather than dropped or split on a guess. An agent reading a header-placed
                  figure should know it still carries the misattribution this join exists to
                  correct. */}
              {apprCounty.basis && (apprCounty.basis.fromUnit > 0 || apprCounty.basis.fromField > 0) && (
                <div style={{ fontSize: 11, color: '#4a5a4a', marginTop: 6, lineHeight: 1.45 }}>
                  ⓘ Placed by{' '}
                  {apprCounty.basis.fromUnit > 0 && <><b>insured unit</b> ({apprCounty.basis.fromUnit} line{apprCounty.basis.fromUnit === 1 ? '' : 's'})</>}
                  {apprCounty.basis.fromUnit > 0 && apprCounty.basis.fromField > 0 && ' · '}
                  {apprCounty.basis.fromField > 0 && <><b>FSA farm</b> ({apprCounty.basis.fromField} line{apprCounty.basis.fromField === 1 ? '' : 's'})</>}
                  {apprCounty.basis.moved > 0 && <>
                    {' — '}{apprCounty.basis.moved} re-filed to the county the insurance puts
                    {apprCounty.basis.moved === 1 ? ' it' : ' them'} in rather than the county on the worksheet header.
                  </>}
                </div>
              )}
              {apprCounty.basis && apprCounty.basis.fromHeader > 0 && (
                <div style={{ fontSize: 11, color: '#7a5c00', marginTop: 6, lineHeight: 1.45 }}>
                  ⚠ {apprCounty.basis.fromHeader} appraisal line{apprCounty.basis.fromHeader === 1 ? '' : 's'}
                  {' '}kept the worksheet's own county
                  {apprCounty.basis.ambiguous > 0 && <> ({apprCounty.basis.ambiguous} on a unit or FSA farm whose insured ground spans two counties — the split is not derivable)</>}
                  {apprCounty.basis.unmatched > 0 && <> ({apprCounty.basis.unmatched} matching no insured field on file)</>}
                  . Check {apprCounty.basis.fromHeader === 1 ? 'that line' : 'those lines'} before accepting the figure.
                </div>
              )}
              {/* A unit column that does not hold a unit number is an EXTRACTION defect, not
                  an absent unit, and it is named so a re-upload can fix it. LANG FARMS' four
                  lines carry "1.0000 BU" there — a bushel figure in the wrong column — and
                  are placed by the FSA farm instead. */}
              {apprCounty.basis && apprCounty.basis.unitUnusable > 0 && (
                <div style={{ fontSize: 11, color: '#7a5c00', marginTop: 6, lineHeight: 1.45 }}>
                  ⚠ {apprCounty.basis.unitUnusable} line{apprCounty.basis.unitUnusable === 1 ? '' : 's'}
                  {' '}carr{apprCounty.basis.unitUnusable === 1 ? 'ies' : 'y'} no readable unit number
                  {' '}— placed by the FSA farm instead. Re-uploading the worksheet would correct the column.
                </div>
              )}
              <div style={{ fontSize: 11, color: '#60735f', marginTop: 8, lineHeight: 1.5 }}>
                Attributed <b>{bu(countyRecon.attributed)}</b>
                {countyRecon.total != null && <> of <b>{bu(countyRecon.total)}</b> on file</>}
                {/* Absent is not zero: a blank county is unsettled, and reading it as a
                    total loss is the largest indemnity the policy can pay. */}
                {' · '}a blank county is <b>not settled</b>, not zero.
              </div>
            </div>
          )}
          {cropU === 'CORN' && (
            <div style={{ marginTop: 12, borderTop: '1px dashed #e4ece4', paddingTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                <div style={{ fontWeight: 700, color: '#1a2e1a', fontSize: 13 }}>🌽 Silage appraisals <span style={{ fontWeight: 400, color: '#60735f', fontSize: 11 }}>(chopped acres)</span></div>
                <label style={{ fontSize: 11.5, fontWeight: 700, color: '#2d6a2d', cursor: 'pointer', border: '1px solid #cfe6cf', borderRadius: 7, padding: '5px 10px', background: '#f2f9f2' }}>
                  📄 Upload appraisal PDF
                  <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) onUploadAppraisal(f); e.target.value = ''; }} />
                </label>
              </div>
              {silageImp && <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 8, padding: '6px 10px', borderRadius: 6, background: silageImp.status === 'err' ? '#fbeae5' : silageImp.status === 'ok' ? '#e8f6ec' : '#f2f6f2', color: silageImp.status === 'err' ? '#b3300f' : silageImp.status === 'ok' ? '#2d6a2d' : '#4a5a4a' }}>{silageImp.status === 'busy' ? '⏳ ' : silageImp.status === 'ok' ? '✅ ' : silageImp.status === 'err' ? '⚠ ' : silageImp.status === 'confirm' ? '⚠ ' : ''}{silageImp.msg}
                {silageImp.status === 'confirm' && silageImp.pending && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => finishAppraisalUpload(silageImp.pending, true)} style={{ fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid #b3300f', background: '#fff', color: '#b3300f', cursor: 'pointer' }}>Replace those lines</button>
                    <button onClick={() => finishAppraisalUpload(silageImp.pending, false)} style={{ fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 6, border: '1px solid #2d6a2d', background: '#fff', color: '#2d6a2d', cursor: 'pointer' }}>Add alongside</button>
                    <button onClick={() => setSilageImp(null)} style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 6, border: '1px solid #cfd8cf', background: '#fff', color: '#4a5a4a', cursor: 'pointer' }}>Cancel</button>
                  </div>
                )}</div>}
              {cropSilage.length > 0 ? (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                      <thead><tr style={{ color: '#60735f' }}>
                        <th style={{ textAlign: 'left', padding: '3px 5px', fontWeight: 700 }}>Field</th>
                        <th style={{ textAlign: 'left', padding: '3px 5px', fontWeight: 700 }}>FSA</th>
                        <th style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700 }}>Acres</th>
                        <th style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700 }}>Appraised</th>
                        <th style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700 }}>Bu</th>
                      </tr></thead>
                      <tbody>
                        {cropSilage.map((r, i) => (
                          <tr key={i} style={{ borderTop: '1px solid #f0f4f0', fontVariantNumeric: "tabular-nums" }}>
                            <td style={{ textAlign: 'left', padding: '3px 5px', fontFamily: 'inherit', fontWeight: 700, color: '#1a2e1a' }}>
                              {r.field_name || '—'}
                              {/* How the line's acres are INSURED, as the worksheet's unit header
                                  prints it (Kevin, 2026-11-02: "Should be able identify each silage
                                  field and grain field"). Blank when the form did not state a type. */}
                              {appraisalInsuredType(r) && (
                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: appraisalInsuredType(r) === 'silage' ? '#8a6d1f' : '#60735f' }}>
                                  {appraisalInsuredType(r) === 'silage' ? 'Silage-insured' : 'Grain'}
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: 'left', padding: '3px 5px', color: '#60735f' }}>{r.fsn || '—'}</td>
                            <td style={{ textAlign: 'right', padding: '3px 5px', color: '#4a5a4a' }}>{r.appraised_acres != null ? Number(r.appraised_acres).toFixed(1) : '—'}</td>
                            <td style={{ textAlign: 'right', padding: '3px 5px', color: '#1a2e1a', fontWeight: 700 }}>{r.appraised_yield_bu_ac != null ? `${r.appraised_yield_bu_ac} bu/ac` : (r.notes || '—')}</td>
                            <td style={{ textAlign: 'right', padding: '3px 5px', color: '#1a2e1a', fontWeight: 700 }}>{r.appraised_bu != null ? bu(r.appraised_bu) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                      {/* ⚠ THE ACRES ARE SUMMED ON THE TABLE, not only inside the checkbox
                          label below it. Kevin, 2026-10-06: "appraised bushels by field, and
                          appraised acres summed." A 61-row table whose only total sits in a
                          control under it makes the reader add a column by eye to answer the
                          one question the table is for — how much ground this appraisal
                          covers. Both totals are the SAME figures the checkbox counts, read
                          from the same two variables, so the foot and the control can never
                          disagree about what is being added. */}
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #cfdccf', fontVariantNumeric: "tabular-nums", fontWeight: 800 }}>
                          <td style={{ textAlign: 'left', padding: '5px 5px', fontFamily: 'inherit', color: '#1a2e1a' }}>
                            {apprSum.silageInsuredLines > 0
                              ? `${cropSilage.length - apprSum.silageInsuredLines} grain field${cropSilage.length - apprSum.silageInsuredLines === 1 ? '' : 's'}`
                              : `${cropSilage.length} field${cropSilage.length === 1 ? '' : 's'}`}
                          </td>
                          <td />
                          <td style={{ textAlign: 'right', padding: '5px 5px', color: '#1a2e1a' }}>{appraisedAcres.toFixed(1)}</td>
                          <td />
                          <td style={{ textAlign: 'right', padding: '5px 5px', color: '#1a2e1a' }}>{bu(appraisedBu)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, fontWeight: 700, color: '#1a2e1a', cursor: 'pointer' }}>
                    <input type="checkbox" checked={includeSilage} onChange={e => setIncludeSilage(e.target.checked)} />
                    {/* ⚠ THE FIGURES ARE ON THE ROW ABOVE. This label and the table's own
                        total row read the SAME two variables and sat one line apart —
                        "61 fields · 1533.3 · 298,043" and then "Count 298,043 appraised bu
                        (1533 ac) in production". The total is the table's job; this is a
                        control, so it says what it DOES. One of the eight restatements of
                        298,043 on this page, and the only one this commit introduced. */}
                    Count these appraised bushels in production
                  </label>
                  {/* The sheet checks its own arithmetic — say what that check found.
                      "unchecked" is its OWN answer and must never read as verified:
                      the NAU form states an acre appraisal without printing the
                      working, so there is nothing to reproduce. A FAILED row is kept
                      and flagged rather than dropped — dropping it would read as "no
                      production on these acres", which is certainly wrong. */}
                  <div style={{ fontSize: 10.5, marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    {apprSum.verified > 0 && (
                      <span style={{ color: '#2d6a2d', fontWeight: 700 }} title="Every arithmetic identity the worksheet printed reproduces: sample weight x yield factor = the per-stage appraisal, the stages sum to the stated total, and that total divided by the plot count gives the acre appraisal.">
                        ✅ {apprSum.verified} field{apprSum.verified === 1 ? '' : 's'} check out against the sheet's own arithmetic
                      </span>
                    )}
                    {apprSum.unchecked > 0 && (
                      <span style={{ color: '#60735f', fontWeight: 700 }} title="The worksheet states a per-acre appraisal but prints no working to check it against. Taken as printed — this is not a failure, and it is not a verification either.">
                        ○ {apprSum.unchecked} taken as printed (no working on the page)
                      </span>
                    )}
                    {apprSum.failed > 0 && (
                      <span style={{ color: '#b3300f', fontWeight: 800 }} title="An identity the worksheet DID state does not reproduce. The row is kept with the figures as printed — check the PDF against what was read before this production is counted.">
                        ⚠ {apprSum.failed} do NOT reproduce — check the worksheet
                      </span>
                    )}
                  </div>
                  {apprSum.silageInsuredLines > 0 && (
                    <div style={{ fontSize: 10.5, marginTop: 5, color: '#8a6d1f', fontWeight: 600 }}>
                      Not in the grain total: {bu(apprSum.silageInsuredBu)} bu appraised on {apprSum.silageInsuredLines} silage-insured field{apprSum.silageInsuredLines === 1 ? '' : 's'} ({apprSum.silageInsuredAcres.toFixed(1)} ac). Those acres are insured as silage, so their bushels are not grain production.
                    </div>
                  )}
                  {(apprSum.ton > 0 || apprSum.lb > 0 || apprSum.unknownUnitAcres > 0) && (
                    <div style={{ fontSize: 10.5, marginTop: 5, color: '#8a6d1f', fontWeight: 600 }}>
                      Not counted in the bushel total:
                      {apprSum.ton > 0 && <> {apprSum.ton.toLocaleString()} ton on silage-coded fields ·</>}
                      {apprSum.lb > 0 && <> {apprSum.lb.toLocaleString()} lb on popcorn-coded fields ·</>}
                      {apprSum.unknownUnitAcres > 0 && <> {apprSum.unknownUnitAcres.toFixed(0)} ac whose appraisal code could not be read ·</>}
                      {' '}a tonnage is not a bushel and is never added to one.
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: '#60735f', marginTop: 4, lineHeight: 1.45 }}>How an appraised field is counted: chopped → the appraisal is the production (a chopper&rsquo;s tonnage is not grain); combined → the combine figure; appraised and then combined → the combine figure, not both. Back an appraisal out with the toggle if it is double-counted elsewhere.</div>
                </>
              ) : (
                <div style={{ fontSize: 11.5, color: '#60735f' }}>No appraisals on file. Upload the AIP corn/silage appraisal worksheet to add chopped-acre production.</div>
              )}
              {fsnCoverage && (() => {
                const p = fsnCoverage.pct;
                const covColor = p == null ? '#60735f' : p >= 0.9 ? '#2d6a2d' : p >= 0.5 ? '#b8860b' : '#b34a10';
                return (
                  <details style={{ marginTop: 12, borderTop: '1px dashed #e4ece4', paddingTop: 10 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#2d4a2d', padding: '2px 0' }}>Details — appraisal coverage by FSA farm</summary>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                      <div style={{ fontWeight: 700, color: '#1a2e1a', fontSize: 13 }}>📋 Appraisal coverage <span style={{ fontWeight: 400, color: '#60735f', fontSize: 11 }}>(PLANTED corn acres appraised, by farm)</span></div>
                      {p != null && <div style={{ fontSize: 13, fontWeight: 800, color: covColor }}>{(p * 100).toFixed(0)}% appraised</div>}
                    </div>
                    <div style={{ height: 8, background: '#eef3ee', borderRadius: 5, overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{ width: `${Math.min(100, (p || 0) * 100)}%`, height: '100%', background: covColor, transition: 'width .3s' }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#4a5a4a', marginBottom: 8, lineHeight: 1.45 }}>
                      {fsnCoverage.apprTot.toFixed(0)} of {fsnCoverage.plantedTot.toFixed(0)} <strong>planted</strong> ac appraised across {fsnCoverage.fsnsWithAppr}/{fsnCoverage.fsnsTot} farms{fsnCoverage.unaccountedTot > 1
                        ? <> · <strong style={{ color: '#b34a10' }}>{fsnCoverage.unaccountedTot.toFixed(0)} planted ac not appraised</strong> — expect grain harvest (tickets / JD yield) on those; if none shows up, go looking for production.</>
                        : <> · <strong style={{ color: '#2d6a2d' }}>every planted acre is accounted for.</strong></>}
                      {/* ⚠ OVER 100% IS INFORMATION, NOT AN ERROR, AND IT HAS TO SAY SO.
                          This panel groups by FSA FARM, and the only per-FSN acreage we hold is
                          `policy_fields` — the FIELD MAP. The appraisal worksheet covers acres the
                          map has no row for, so the ratio can exceed 100 and the totals here are
                          SMALLER than the policy's own: MULLIGAN reads 1,537 insured / 1,509
                          planted here against 1,561 / 1,533 at the top of the card. Two bases, one
                          card — the thing that produced every wrong percentage on it — so the one
                          that cannot be changed states which it is.
                          The ring above answers the same question on the POLICY basis and reads
                          100%; nothing here is a second opinion about whether the farm is covered. */}
                      {fsnCoverage.apprTot > fsnCoverage.plantedTot + 0.05 && (
                        <> <span style={{ color: '#7a6a2a' }}>Over 100% because the worksheet covers
                        {' '}{(fsnCoverage.apprTot - fsnCoverage.plantedTot).toFixed(0)} ac more than the field map
                        {' '}holds for these farms — an acreage finding, not extra coverage.</span></>
                      )}
                      <> <span style={{ color: '#60735f' }}>Acreage on this panel is the field map,
                      {' '}which is why it can differ from the policy totals at the top of the card.</span></>
                      {/* ⚠ PP acres are stated, never folded into the denominator. They were
                          never in the ground, so they can carry neither an appraisal nor a
                          grain ticket, and counting them as "not appraised" starts a hunt
                          that cannot end — LANG CY2026 read 38% and 61 ac missing when the
                          planted acres were in fact 100% appraised. */}
                      {fsnCoverage.ppTot > 0.05 && (
                        <> <span style={{ color: '#7a6a2a' }}>{fsnCoverage.ppTot.toFixed(0)} of the {fsnCoverage.insuredTot.toFixed(0)} insured ac are <strong>prevent plant</strong> — settled on the PP claim, never planted, so no appraisal or ticket can exist for them.</span></>
                      )}
                      {/* The insured-acre DENOMINATOR is the field map, which only exists
                          for years the M13 backfill has written. Borrowing another year's
                          acres changes every percentage on this panel, so it is stated. */}
                      {insuredFieldsYear != null && Number(insuredFieldsYear) !== Number(year) && (
                        <> <span style={{ color: '#8a5a00', fontWeight: 700 }}>Insured acres are the {insuredFieldsYear} field map — no {year} map on file, so these percentages are against another year's acreage.</span></>
                      )}
                    </div>
                    {/* ⚠ THE HEADLINE IS THE ANSWER; THE TABLE IS THE WORKING.
                        "102% appraised · 1533 of 1509 planted ac across 11/11 farms · every
                        planted acre is accounted for" is what a reader needs, and it is
                        already above this. The twelve rows under it are how that was
                        arrived at — worth having, not worth reading every time, and on a
                        phone they are most of the page.

                        FOLDED, NOT DELETED, for the same reason as the field roll call: a
                        farm that is NOT 100% is the thing somebody has to act on, and only
                        the rows show which one. Mulligan's 6198 is 806.4 appraised against
                        781.9 planted — the 103% that makes his total read 102%. */}
                    <details style={{ marginTop: 2 }}>
                      <summary style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 700, color: '#2d4a2d', padding: '4px 0' }}>
                        Per-farm working ({fsnCoverage.rows.length} FSA farm{fsnCoverage.rows.length === 1 ? '' : 's'})
                      </summary>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                        <thead><tr style={{ color: '#60735f' }}>
                          <th style={{ textAlign: 'left', padding: '3px 5px', fontWeight: 700 }}>FSA Farm</th>
                          <th style={{ textAlign: 'left', padding: '3px 5px', fontWeight: 700 }}>County</th>
                          <th style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700 }}>Insured ac</th>
                          <th style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700 }}>PP ac</th>
                          <th style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700 }}>Planted ac</th>
                          <th style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700 }}>Appraised ac</th>
                          <th style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700 }}>% appraised</th>
                        </tr></thead>
                        <tbody>
                          {fsnCoverage.rows.map((r, i) => {
                            const noIns = r.insured <= 0.05, none = r.appraised <= 0.05, full = r.pct != null && r.pct >= 0.9;
                            // An all-PP farm is SETTLED, not missing: neutral ground, and the
                            // % cell says so in words rather than printing a 0% that reads as
                            // a gap somebody has to chase.
                            const bg = noIns ? '#fdf6e7' : r.allPp ? '#f6f6f2' : none ? '#faf3ee' : full ? '#f2f9f2' : '#fffcef';
                            return (
                              <tr key={i} style={{ borderTop: '1px solid #f0f4f0', background: bg, fontVariantNumeric: "tabular-nums" }}>
                                <td style={{ textAlign: 'left', padding: '3px 5px', fontWeight: 700, color: '#1a2e1a' }}>{r.fsn}</td>
                                <td style={{ textAlign: 'left', padding: '3px 5px', fontFamily: 'inherit', color: '#60735f' }}>{r.county || '—'}</td>
                                <td style={{ textAlign: 'right', padding: '3px 5px', color: '#4a5a4a' }}>{r.insured > 0.05 ? r.insured.toFixed(1) : '—'}</td>
                                <td style={{ textAlign: 'right', padding: '3px 5px', color: r.pp > 0.05 ? '#7a6a2a' : '#c2ccc2' }}>{r.pp > 0.05 ? r.pp.toFixed(1) : '—'}</td>
                                <td style={{ textAlign: 'right', padding: '3px 5px', color: '#4a5a4a' }}>{r.planted > 0.05 ? r.planted.toFixed(1) : '—'}</td>
                                <td style={{ textAlign: 'right', padding: '3px 5px', color: '#1a2e1a', fontWeight: 700 }}>{r.appraised > 0.05 ? r.appraised.toFixed(1) : '—'}</td>
                                <td style={{ textAlign: 'right', padding: '3px 5px', fontWeight: 700, fontFamily: 'inherit', color: noIns ? '#b8860b' : r.allPp ? '#7a6a2a' : none ? '#60735f' : full ? '#2d6a2d' : '#b34a10' }}>
                                  {noIns ? '⚠ no insured match' : r.allPp ? 'all prevent plant' : r.pct != null ? `${(r.pct * 100).toFixed(0)}%` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    </details>
                    <div style={{ fontSize: 10, color: '#60735f', marginTop: 4, lineHeight: 1.45 }}>Tied to insured fields by <strong>FSA Farm No.</strong> (appraisals don't carry a CLU-matchable field id). A “⚠ no insured match” farm means the appraisal's FSN isn't on this year's insured fields — usually FSA farm reconstitution; verify the farm.</div>
                  </details>
                );
              })()}
            </div>
          )}
          {savedNote && !persistErr.inputsSave && <div style={{ fontSize: 10, color: '#60735f', marginTop: 6 }}>✓ Saved — imported loads and these entries reload when you return to this farm.</div>}
          {[['inputsLoad', "Couldn't load this farm's saved claim entries — changes are not being saved until they load"],
            ['countyLoad', "Couldn't load this farm's county bushels — they are not being saved until they load"],
            ['inputsSave', "NOT saved — the claim entries failed to save"],
            ['countySave', "NOT saved — the county bushels failed to save"]]
            .filter(([k]) => persistErr[k]).map(([k, msg]) => (
              <div key={k} style={{ fontSize: 11, color: '#8a3324', fontWeight: 600, marginTop: 6 }}>⚠ {msg}: {persistErr[k]}</div>
            ))}
          <div style={{ borderTop: '1px solid #eef3ee', marginTop: 12, paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 12.5, color: '#4a5a4a', fontWeight: 700 }}>
              Total production entered{acctRead && acctRead.stillNeed > 0.05 ? ' so far' : ''}
            </span>
            <span style={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{bu(production)} <span style={{ fontSize: 12, color: '#60735f' }}>{cropUnit}</span></span>
          </div>
          {/* Kevin, 2026-10-07: "this is the total production thus far (even though we are
              expecting a lot more production)". A running total presented as a total is what
              let 308 acres' worth of bushels read as the farm's answer — so where the counted
              production covers only part of the ground, the heading says SO FAR and the acres
              behind it are stated here rather than left to be inferred from the claim card. */}
          {acctRead && acctRead.stillNeed > 0.05 && (
            <div style={{ fontSize: 11, color: '#7a5c00', marginTop: 4, fontWeight: 700, lineHeight: 1.45 }}>
              ⚠ <b>{bu(acctRead.stillNeed)}</b> of <b>{bu(acctRead.planted)}</b> planted ac have no production figure yet — see the by-county table above.
            </div>
          )}
          <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 4 }}>RMA counts ALL production — delivered + stored + saved seed + fed. Delivered defaults to RMA net{manualDelivered !== '' ? ' (overridden)' : ''}.</div>
          {/* Machine yield is a CHECK beside production, never part of it. More than 10%
              apart is flagged — admin only, like every claim estimate on this screen. */}
          {canSeeClaimEstimate && hc.machineCheck.compared && (
            <div style={{ fontSize: 11, marginTop: 4, fontWeight: hc.machineCheck.flag ? 700 : 400, color: hc.machineCheck.flag ? '#8a3324' : '#60735f' }}>
              {hc.machineCheck.flag ? '⚠ ' : ''}Machine yield {bu(hc.machineCheck.machineBu)} {cropUnit} vs measured {bu(hc.machineCheck.measuredBu)}
              {' '}({Math.round(hc.machineCheck.diffPct * 100)}% apart{hc.machineCheck.flag ? ' — more than 10%, check tickets and the monitor' : ''}). Not added to production.
            </div>
          )}
          {/* ⚠ THE THREE-WAY RECONCILIATION TABLE WAS DELETED HERE, and this note is why,
              because its absence reads like an oversight. It printed four rows — delivered,
              production to count, reported to crop insurance, and the guarantee — every one
              of which is already on this screen: delivered and the guarantee lead the page,
              production to count is the sum of the four boxes directly above this line, and
              what the carrier holds is the first line of the production-report card. It was
              the single largest source of the repetition Kevin named on 2026-10-03: the
              guarantee appeared FIVE times on one modal and this table was two of them.
              What it uniquely said — that the three measure different things and a gap is
              normal — survives on the sources list, which names every channel and what
              outranks what. Do not restore it without first checking those four figures are
              still stated elsewhere. */}
          {/* ── Loads, and moving one to another crop year ──────────────────
              crop_year is DERIVED from the delivery date, which is right almost
              always and wrong in one real case: a grower emptying a bin of last
              year's corn in November reads as this year's production. On a claim
              that is the wrong crop year.

              Assignment is held by a database trigger (sql/039), not by code
              here, because BOTH writers would otherwise overwrite it — the CSV
              import on its next upload and the Bushel feed every hour. That is
              why the save below is two statements: unlock, then assign-and-lock.
              An import carries crop_year but never carries crop_year_locked, so
              on a locked row it is always the preserved case and there is no
              ordering in which it wins.

              Every crop year is listed, not just the selected one, because a
              load that landed in the wrong year has to be reachable FROM the
              year you are working. */}
          {cropAllYears.length > 0 && (
            <div style={{ marginTop: 12, border: '1px solid #e4ece4', borderRadius: 8, overflow: 'hidden' }}>
              <div onClick={() => setShowLoads(v => !v)}
                style={{ background: '#f7faf6', padding: '7px 11px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: '#1a2e1a' }}>
                  {showLoads ? '▾' : '▸'} Loads on file — {crop.toLowerCase()}
                </div>
                <div style={{ fontSize: 10.5, color: '#60735f' }}>
                  {cropTickets.length} in {year}
                  {cropAllYears.length - cropTickets.length > 0 ? ` · ${cropAllYears.length - cropTickets.length} in other years` : ''}
                  {lockedCount > 0 ? ` · ${lockedCount} assigned by hand` : ''}
                </div>
              </div>

              {showLoads && (
                <div style={{ padding: '4px 11px 10px' }}>
                  {loadsByYear.map(([yr, list]) => (
                    <div key={yr} style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.05em',
                        color: Number(yr) === Number(year) ? '#2d6a1a' : '#677167', padding: '3px 0' }}>
                        {yr === 'null' ? 'NO CROP YEAR' : yr}{Number(yr) === Number(year) ? '  ← this claim' : ''}
                        <span style={{ fontWeight: 600, color: '#677167' }}>
                          {'  ·  '}{list.length} load{list.length === 1 ? '' : 's'}{'  ·  '}
                          {bu(list.reduce((s, t) => s + (Number(t.net_bushels) || 0), 0))} bu
                        </span>
                      </div>
                      {list.slice(0, loadLimit).map(t => (
                        <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 11.5, cursor: 'pointer', borderBottom: '1px solid #f6f9f6' }}>
                          <input type="checkbox" checked={selectedLoads.has(t.id)}
                            onChange={e => setSelectedLoads(s => {
                              const n = new Set(s); if (e.target.checked) n.add(t.id); else n.delete(t.id); return n;
                            })} />
                          <span style={{ fontVariantNumeric: "tabular-nums", color: '#4a5a4a', minWidth: 78 }}>{t.delivery_date || '—'}</span>
                          <span style={{ flex: '1 1 100px', minWidth: 0, color: '#1a2e1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.buyer || '—'}</span>
                          <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: '#1a2e1a', whiteSpace: 'nowrap' }}>{bu(t.net_bushels)} bu</span>
                          {t.source === 'bushel' && <span title="From the Bushel feed" style={{ fontSize: 10, background: '#eef4ee', color: '#2d4a2d', borderRadius: 4, padding: '1px 5px' }}>feed</span>}
                          {t.crop_year_locked && <span title="Crop year assigned by hand — imports cannot change it" style={{ fontSize: 10, background: '#fdf3e8', color: '#8a5a1a', borderRadius: 4, padding: '1px 5px' }}>🔒 assigned</span>}
                        </label>
                      ))}
                      {list.length > loadLimit && (
                        <button onClick={() => setLoadLimit(n => n + 200)}
                          style={{ border: 'none', background: 'none', color: '#2d4a2d', fontSize: 10.5, fontWeight: 700, cursor: 'pointer', padding: '3px 0' }}>
                          show {Math.min(200, list.length - loadLimit)} more of {list.length}
                        </button>
                      )}
                    </div>
                  ))}

                  <div style={{ marginTop: 9, paddingTop: 8, borderTop: '1px solid #eef3ee', display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: selectedLoads.size ? '#1a2e1a' : '#677167', fontWeight: 700 }}>
                      {selectedLoads.size || 'no'} load{selectedLoads.size === 1 ? '' : 's'} selected
                    </span>
                    <select value={assignYear} onChange={e => setAssignYear(e.target.value)}
                      style={{ font: 'inherit', fontSize: 11.5, padding: '4px 8px', borderRadius: 6, border: '1px solid #cfdccf' }}>
                      {assignableYears.map(y => <option key={y} value={y}>{y} crop</option>)}
                    </select>
                    <button disabled={!selectedLoads.size || !!assigning}
                      onClick={() => assignCropYear([...selectedLoads], Number(assignYear))}
                      style={{ font: 'inherit', fontSize: 11.5, fontWeight: 700, cursor: selectedLoads.size ? 'pointer' : 'default',
                        border: '1px solid #2d4a2d', background: selectedLoads.size ? '#2d4a2d' : '#cfdccf', color: '#fff', borderRadius: 6, padding: '4px 11px' }}>
                      {assigning === 'assign' ? 'Moving…' : 'Assign to crop year'}
                    </button>
                    <button disabled={!selectedLoads.size || !!assigning}
                      onClick={() => releaseCropYear([...selectedLoads])}
                      title="Drop the hand assignment and go back to the date rule"
                      style={{ font: 'inherit', fontSize: 11.5, fontWeight: 700, cursor: selectedLoads.size ? 'pointer' : 'default',
                        border: '1px solid #cfdccf', background: '#fff', color: '#2d4a2d', borderRadius: 6, padding: '4px 11px' }}>
                      {assigning === 'release' ? 'Releasing…' : 'Back to the date rule'}
                    </button>
                    {loadErr && <span style={{ fontSize: 11, color: '#c0392b' }}>{loadErr}</span>}
                  </div>
                  <div style={{ fontSize: 10.5, color: '#677167', marginTop: 6, lineHeight: 1.5 }}>
                    A load belongs to the crop harvested before it was delivered — wheat from July 1, corn and
                    soybeans from October 1. Assign by hand only when that is wrong, which is usually old-crop
                    grain coming out of a bin. An assigned load keeps its year through every future import;
                    its bushels, buyer and moisture still update.
                  </div>
                </div>
              )}
            </div>
          )}
          {/* ⚠ NO PER-ACRE YIELD WHILE ANY PLANTED ACRE HAS NO PRODUCTION FIGURE. Dividing a partial
              production by every planted acre counts the rest as ZERO — Red Dawn read 111.4 bu/ac,
              -69 vs APH, in red, with 2,367 acres un-accounted. It appears once accounting is complete. */}
          {yieldPerAc != null && acctRead && acctRead.stillNeed <= 0.05 && (
            <div style={{ marginTop: 10, background: '#f2f9f2', border: '1px solid #cfe6cf', borderRadius: 8, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div>
                <div style={lbl}>Scaled yield / acre</div>
                {/* ⚠ THE DENOMINATOR IS NAMED, AND THE PP ACRES ARE NAMED BESIDE IT. The
                    label used to read "insured ac" while dividing by the insured figure —
                    correct-looking and wrong, because no bushel comes off a prevent-planted
                    acre. Stating both numbers is what lets an agent check it against the
                    grower's own acreage report. */}
                <div style={{ fontSize: 10.5, color: '#60735f' }}>{bu(production)} bu ÷ {bu(grainPlantedAc)} planted ac{aphWtd != null ? ` · APH ${aphWtd.toFixed(1)}` : ''}</div>
                {ppAcresCov > 0.05 && (
                  <div style={{ fontSize: 10, color: '#7a6a2a' }}>{bu(insuredAcres)} insured less {bu(ppAcresCov)} prevent plant — PP acres grew nothing, so they are not in the divisor</div>
                )}
                {/* ⚠ THE SAME ZERO-ASSUMPTION THE CLAIM READ NOW REFUSES, on the number that
                    makes it look measured. The divisor is right; it is the DIVIDEND that is
                    partial, so every uncovered acre lands in this figure as zero bushels.
                    Rogers Farms prints 54.5 bu/ac and "−128.5 vs APH" off 308 appraised acres
                    that are themselves running 196 bu/ac, ABOVE his 183 APH. The verdict is
                    withheld above; this number cannot be withheld — it is the running total —
                    so it is LABELLED instead, which is the same rule the chopped-acre and
                    PP-acre notes beside it already follow. */}
                {prodPartial && (
                  <div style={{ fontSize: 10, color: '#b3300f', fontWeight: 700 }}>
                    ⚠ production covers {bu(acreScopedAc)} of these acres — the other {bu(uncoveredAc)} ac
                    count as ZERO here, so this is a floor, not a yield
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div><span style={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{yieldPerAc.toFixed(1)}</span><span style={{ fontSize: 12, color: '#60735f' }}> bu/ac</span></div>
                {aphWtd != null && <div style={{ fontSize: 11, fontWeight: 700, color: yieldPerAc >= aphWtd ? '#2d6a2d' : '#b3300f' }}>{yieldPerAc >= aphWtd ? '+' : ''}{(yieldPerAc - aphWtd).toFixed(1)} bu vs APH</div>}
              </div>
            </div>
          )}
          {cov?.incomplete?.length > 0 && insuredAcres > 0 && (
            <div style={{ fontSize: 10, color: '#b34a10', marginTop: 4 }}>Note: {cov.incomplete.length} unit{cov.incomplete.length > 1 ? 's' : ''} missing acres — scaled yield divides by the {bu(plantedAcres)} planted ac we have, so it reads high if production came off those acres too.</div>
          )}
          {revToCount != null && production > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8, borderTop: '1px dashed #e4ece4', paddingTop: 8 }}>
              <span style={{ fontSize: 12.5, color: '#4a5a4a', fontWeight: 700 }}>Actual revenue <span style={{ fontWeight: 400, color: '#60735f' }}>({bu(production)} × {px(hp)} {harvest?.isFinal ? 'final' : 'est'})</span></span>
              <span style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{money(revToCount)}</span>
            </div>
          )}
        </div>

        {/* ── JOHN DEERE per-county yield (reference cross-check) ── */}
        {jdData && jdData.byCounty && Object.keys(jdData.byCounty).length > 0 && (
          <div style={{ ...card, gridColumn: isMobile ? 'auto' : '1 / -1' }}>
            {/* ⚠ COLLAPSED. Every figure this table leads with is now stated once above it:
                the monitor total and its source on the read, the insured-against-harvested
                acres beside them, and the guarantee on the same card. What is behind the
                control is the per-COUNTY split and the bu/ac, which nothing else states —
                worth keeping, not worth being the fourth printing of 78,057. */}
            <button type="button" onClick={() => setJdOpen(v => !v)}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: jdOpen ? 8 : 0, minHeight: 44 }}>
              <span style={{ fontWeight: 800, color: '#1a2e1a', fontSize: 15, flexGrow: 1 }}>🚜 John Deere yield by county</span>
              <span style={{ fontSize: 10.5, color: '#7a5b00', background: '#fdf6e7', padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>MONITOR ESTIMATE</span>
              <span style={{ fontSize: 12.5, color: '#5a6a5a' }}>{jdOpen ? 'Hide' : 'Show'}</span>
            </button>
            {jdOpen && (<>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: '#60735f' }}>
                    <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 700 }}>County</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>APH</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>Ins / JD ac</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>JD yield/ac</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>JD prod</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>Guar</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>% guar</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(jdData.byCounty).filter(c => (jdData.byCounty[c].acres || 0) >= 0.5).sort().map(county => {
                    const jc = jdData.byCounty[county];
                    const unit = (cov?.units || []).find(u => String(u.county).toUpperCase() === String(county).toUpperCase());
                    const guarBu = unit?.buGuar || null;
                    const aph = unit?.aph || null;
                    // Planted, not insured: buGuar is the PLANTED guarantee (PP is paid as PP).
                    const insAc = (unit?.plantedAcres != null ? unit.plantedAcres : unit?.acres) || null;
                    const ypa = jc.acres ? jc.prod / jc.acres : null;
                    const pct = guarBu ? jc.prod / guarBu : null;
                    const under = pct != null && pct < 1;
                    // Color JD yield/ac RED only when below the GUARANTEE yield (APH × coverage
                    // = buGuar ÷ insured acres) — a real potential loss, not merely below APH.
                    const guarYpa = (guarBu && insAc) ? guarBu / insAc : null;
                    const yBelowGuar = (ypa != null && guarYpa) ? ypa < guarYpa : null;
                    const acresGap = (insAc && jc.acres < insAc * 0.9);
                    return (
                      <tr key={county} style={{ borderTop: '1px solid #eef3ee', fontVariantNumeric: "tabular-nums" }}>
                        <td style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 700, color: '#1a2e1a', fontFamily: 'inherit' }}>{county}</td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', color: '#4a5a4a' }}>{aph != null ? aph : '—'}</td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', color: acresGap ? '#b3300f' : '#4a5a4a' }} title={acresGap ? 'JD has fewer acres than insured — production % is understated' : ''}>{insAc != null ? insAc.toFixed(0) : '—'} / {jc.acres.toFixed(0)}{acresGap ? ' ⚠' : ''}</td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 800, color: (ypa == null || guarYpa == null) ? '#60735f' : (yBelowGuar ? '#b3300f' : '#2d6a2d') }} title="JD yield/ac — red only if below the guarantee yield (APH × coverage). % shown is of APH.">{ypa != null ? ypa.toFixed(1) : '—'}{ypa != null && aph ? <span style={{ fontWeight: 400, color: '#60735f' }}> ({Math.round(100 * ypa / aph)}%)</span> : ''}</td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', color: '#1a2e1a', fontWeight: 700 }}>{bu(jc.prod)}</td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', color: '#4a5a4a' }}>{guarBu != null ? bu(guarBu) : <span style={{ color: '#b8c4b8' }}>no cov</span>}</td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 800, color: pct == null ? '#60735f' : (under ? '#b3300f' : '#2d6a2d') }}>{pct != null ? (under ? '▼ ' : '▲ ') + Math.round(pct * 100) + '%' + (acresGap ? '*' : '') : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* ⚠ METHOD, NOT FINDING — and six lines of it on every farm, every crop, every
                year. It is kept word for word because each clause is a defect somebody paid
                for (the red threshold is the GUARANTEE not the APH; the % understates when JD
                has fewer acres), but it belongs one tap away rather than between the reader and
                the next number. */}
            <details style={{ marginTop: 6 }}>
              <summary style={{ fontSize: 10.5, color: '#60735f', cursor: 'pointer' }}>How to read this table</summary>
            <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 6, lineHeight: 1.5 }}>From John Deere Operations Center (all harvested acres — captures binned/fed grain the tickets miss). <strong>JD yield/ac goes red only when it's below the guarantee (APH × coverage) — the actual loss threshold</strong> (the % beside it is vs APH, for context — being under APH but over the guarantee is fine). The production <strong>% guar</strong> understates whenever JD has fewer acres than insured (<span style={{ color: '#b3300f' }}>⚠ / *</span> — fields not connected or split across counties), so read yield/ac, not that %. Monitor estimate, not claim-of-record — file from scale tickets / settlement.</div>
            </details>
            </>)}
          </div>
        )}

        {/* ═══ STAGE 3 ═══ */}
        <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1', margin: '2px 0 -4px',
                      display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                         width: 21, height: 21, borderRadius: 11, background: '#2d4a2d',
                         color: '#fff', fontSize: 11.5, fontWeight: 800, flex: '0 0 auto' }}>3</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#1a2e1a',
                         letterSpacing: '.02em' }}>THE CLAIM</span>
          <span style={{ fontSize: 11.5, color: '#60735f' }}>production against the guarantee</span>
        </div>
        {/* ── GUARANTEE ── */}
        <div style={card}>
          {/* ⚠ COLLAPSED, BECAUSE THE TOP CARD ALREADY STATES THE GUARANTEE. What is behind
              this control is the DERIVATION — the per county/unit split, the plan, and the
              projected and harvest prices it was computed from. Open, it is the same four
              figures a third and fourth time; closed, it is one line a reader opens when they
              want to check the arithmetic. The headline it used to carry led the page before
              this; it leads the page now, once. */}
          <button type="button" onClick={() => setGuarOpen(v => !v)}
            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, marginBottom: guarOpen ? 12 : 0, minHeight: 44 }}>
            <span style={{ fontWeight: 800, color: '#1a2e1a', fontSize: 15, flexGrow: 1 }}>{year} {crop} guarantee by county &amp; unit</span>
            <span style={{ fontSize: 12.5, color: '#5a6a5a' }}>{guarOpen ? 'Hide' : 'Show'}</span>
          </button>
          {guarOpen && (<>
          {!cov || !cov.guaranteeBu ? (
            <div style={{ color: '#b34a10', fontSize: 13, lineHeight: 1.5 }}>
              No base {crop.toLowerCase()} coverage found for {year}. {cov?.hasTopUp ? `Only ${cov.topUpPlans.join('/')} top-up bands are on file (area plans — they can't be checked this way).` : 'Confirm the policy is keyed in crop_coverage.'}
              {/* Naming what the farm DOES insure turns "no coverage" from a dead end
                  into the next click. The picker offers every one of these. */}
              {cropsHeld.length > 0 && (
                <div style={{ marginTop: 6, color: '#60735f' }}>
                  This farm's {year} coverage: {cropsHeld.map(c => claimCropLabel(c.crop)).join(' · ')}.
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div><div style={lbl}>Production guarantee</div><div style={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{bu(cov.guaranteeBu)}</div><div style={{ fontSize: 10.5, color: '#60735f' }}>{cropUnit}{cropUnitAssumed ? '?' : ''} · {cov.plantedAcres != null ? bu(cov.plantedAcres) + ' planted ac' : (cov.acres ? bu(cov.acres) + ' ac' : 'acres n/a')}</div></div>
                {/* ⚠ A REVENUE GUARANTEE ONLY EXISTS ON A REVENUE PLAN. Measured: RMA
                    publishes a harvest price for corn, soybeans and wheat and for NONE
                    of the eleven other crops in this book — they are APH/YP products,
                    where the guarantee is in units and a loss is a yield shortfall
                    valued at the established price. Printing a revenue guarantee over a
                    potato policy invents a product the grower did not buy, which is the
                    same class as drawing last year's coverage on this year's lattice. */}
                {isRevenueBasis
                  ? <div><div style={lbl}>Revenue guarantee</div><div style={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{money(revGuar)}</div><div style={{ fontSize: 10.5, color: '#60735f' }}>@ {px(guarPrice)} guar. price</div></div>
                  : <div><div style={lbl}>Revenue guarantee</div><div style={{ fontSize: 15, fontWeight: 700, color: '#60735f' }}>not this plan</div><div style={{ fontSize: 10.5, color: '#60735f' }}>{(cov.plans || []).join(', ') || 'plan not stated'}</div></div>}
              </div>
              {/* Prevent plant is paid as prevent plant, never as a harvest shortfall, so
                  its liability is outside the guarantee above and named here, not dropped. */}
              {cov.ppLiability > 0 && (
                <div data-claim-pp-liability style={{ fontSize: 11.5, color: '#4a5a4a', background: '#f4f8f4', border: '1px solid #e4ece4', borderRadius: 7, padding: '7px 9px', marginBottom: 12, lineHeight: 1.5 }}>
                  PP liability <strong>{money(cov.ppLiability)}</strong>{cov.ppAcres ? <> on {bu(cov.ppAcres)} PP ac</> : null}, paid separately — not part of this guarantee.{cov.ppLiabilityModelled ? ' (Split modelled at the prevent-plant factor; unit detail not on file.)' : ''}
                </div>
              )}
              {/* WHY it is withheld, in one sentence from the shared rule — so this page
                  and the Claims Manager board cannot explain the same withholding
                  differently. Null on a revenue plan, where the test simply applies. */}
              {cropBasisNote && (
                <div style={{ fontSize: 11.5, color: '#4a5a4a', background: '#f4f8f4', border: '1px solid #e4ece4', borderRadius: 7, padding: '7px 9px', marginBottom: 12, lineHeight: 1.5 }}>
                  {cropBasisNote}
                </div>
              )}
              {cropUnitAssumed && (
                <div style={{ fontSize: 11.5, color: '#b34a10', marginBottom: 12, lineHeight: 1.5 }}>
                  ⚠ The unit for {claimCropLabel(cropU)} is not established in this app — figures below are shown in bushels because a blank column reads as broken, not because bushels is known to be right. Confirm against the policy before using any number here.
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, color: '#4a5a4a', borderTop: '1px solid #eef3ee', paddingTop: 10 }}>
                <span>Plan <strong>{cov.plans.join(', ') || 'RP'}</strong></span>
                <span>Projected <strong>{px(projected)}</strong></span>
                {/* ⚠ "PENDING" IS A PROMISE THAT ONE IS COMING. On a yield plan none ever
                    is — RMA publishes no harvest price for an APH/YP product (measured: 0
                    rows across all eleven of this book's yield-plan crops, CY2026) — so an
                    amber "pending" would have every potato grower waiting on a number that
                    does not exist. Not-applicable and not-yet are different facts. */}
                <span>Harvest {!isRevenueBasis ? <strong style={{ color: '#60735f' }}>n/a on this plan</strong> : harvestPx == null ? <strong style={{ color: '#b34a10' }}>pending</strong> : <><strong>{px(hp)}</strong> <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 5px', borderRadius: 4, background: harvest.isFinal ? '#e8f6ec' : '#fdf6e7', color: harvest.isFinal ? '#2d6a2d' : '#7a5b00' }}>{harvest.isFinal ? 'FINAL' : 'EST'}</span>{hp < harvestPx ? <span style={{ fontSize: 10, color: '#7a5b00', marginLeft: 4 }}>(capped at 200% of projected; raw {px(harvestPx)})</span> : ''}</>}</span>
                <span>Counties <strong>{cov.counties.join(', ') || '—'}</strong></span>
              </div>
              {cov.units.length > 1 && (
                <div style={{ marginTop: 12, overflowX: 'auto' }}>
                  <div style={{ ...lbl, marginBottom: 6 }}>By county / unit</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ color: '#60735f' }}>
                        <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 700 }}>County</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>Planted ac</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>APH×Cov</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>{cropUnit} guar</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 700 }}>{isRevenueBasis ? 'Revenue guar' : 'Guarantee $'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cov.units.map((u, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #eef3ee' }}>
                          <td style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 700, color: '#1a2e1a' }}>{u.county}{cov.plans.length > 1 ? <span style={{ color: '#60735f', fontWeight: 600 }}> · {u.plan}</span> : ''}</td>
                          <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: "tabular-nums", color: '#4a5a4a' }}>{u.plantedAcres != null ? bu(u.plantedAcres) : '—'}</td>
                          <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: "tabular-nums", color: '#4a5a4a' }}>{u.aph != null ? `${u.aph}×${u.cov ?? '—'}` : '—'}</td>
                          <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: u.buGuar != null ? '#1a2e1a' : '#b34a10' }}>{u.buGuar != null ? bu(u.buGuar) : (u.via === 'none' ? 'verify' : '—')}</td>
                          <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: '#1a2e1a' }}>{u.buGuar != null ? money(u.buGuar * guarPrice) : '—'}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid #d8e6d8' }}>
                        <td style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 800, color: '#1a2e1a' }}>Total</td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: "tabular-nums", fontWeight: 800, color: '#1a2e1a' }}>{cov.plantedAcres != null ? bu(cov.plantedAcres) : '—'}</td>
                        <td></td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: "tabular-nums", fontWeight: 800, color: '#1a2e1a' }}>{bu(cov.guaranteeBu)}</td>
                        <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: "tabular-nums", fontWeight: 800, color: '#1a2e1a' }}>{money(revGuar)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 4 }}>{isRevenueBasis ? 'Revenue guar' : 'Guarantee $'} per county @ {px(guarPrice)} {isRevenueBasis ? '(RP max of projected/harvest)' : '(the established price — a yield plan has no harvest price)'}. Production is still checked whole-operation, not per county.</div>
                </div>
              )}
              {harvestPx != null && !harvest.isFinal && (
                <div style={{ fontSize: 11, color: '#7a5b00', marginTop: 8, lineHeight: 1.45 }}>
                  Estimate = {HARVEST_DISCOVERY[cropU]?.label} · {harvest.days} trading day{harvest.days > 1 ? 's' : ''} through {harvest.lastDay}. RP guarantee uses max(projected, harvest); if harvest holds above projected the guarantee rises. Swaps to RMA FINAL automatically once published.
                </div>
              )}
              {harvestPx != null && !harvest.isFinal && harvestPx > projected && (
                <div style={{ fontSize: 11, color: '#2d6a2d', marginTop: 4 }}>Harvest running <strong>above</strong> projected — guarantee is rising, not falling.</div>
              )}
              {cov.incomplete.length > 0 && (
                <div style={{ fontSize: 11, color: '#b34a10', marginTop: 8, lineHeight: 1.45 }}>
                  ⚠ {cov.incomplete.length} unit{cov.incomplete.length > 1 ? 's' : ''} with incomplete coverage data ({cov.incomplete.map(u => u.county || '?').join(', ')}): {cov.incomplete.some(u => u.via === 'liability') ? 'bushel guarantee back-derived from $ liability where acres are missing. ' : ''}{cov.incomplete.some(u => u.via === 'none') ? 'some units have APH but no acres or liability and are NOT in the totals — verify in crop_coverage.' : ''}
                </div>
              )}
            </>
          )}
          </>)}
        </div>

        {/* ── THE MONEY ──────────────────────────────────────────────────────
            ⚠ THE HEADLINE AND THE 🚩/✅ USED TO LIVE HERE AND NOW LEAD THE PAGE. What is left
            is the half the top card does NOT carry: the dollars. Keeping the verdict in both
            places was the redundancy Kevin named — "Likely claim" twice on one screen, eight
            sections apart, is not emphasis, it is two things a reader has to reconcile.
            It renders only once there IS a read; the "enter production" prompt it used to
            show is now the empty state of the card at the top. */}
        {hasClaim != null && (
        <div style={{ ...card, gridColumn: isMobile ? 'auto' : '1 / -1', background: hasClaim ? '#fbf0ec' : '#eef6ef', borderColor: hasClaim ? '#e0b3a3' : '#cfe6cf' }}>
          <div style={{ fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4a5a4a', fontWeight: 800, marginBottom: 10 }}>
            {hasClaim ? 'What the shortfall is worth' : 'The revenue test'}
          </div>
          <div style={{ display: 'flex', gap: isMobile ? 14 : 26, flexWrap: 'wrap' }}>
            <div><div style={lbl}>Rev. guarantee</div><div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{money(revGuar)}</div></div>
            <div><div style={lbl}>Actual revenue</div><div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{revToCount == null ? 'pending' : money(revToCount)}</div></div>
            <div><div style={lbl}>Bushel short</div><div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: (buShort || 0) > 0 ? '#b3300f' : '#2d6a2d' }}>{buShort == null ? '—' : (buShort > 0 ? bu(buShort) : '0')}</div></div>
            {canSeeClaimEstimate && <div><div style={lbl}>Revenue short</div><div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: (revShort || 0) > 0 ? '#b3300f' : '#2d6a2d' }}>{revShort == null ? 'pending' : money(revShort)}</div></div>}
            {/* ⚠ A CLAIM ESTIMATE IS AN ADMIN TOOL. The Claims Manager also renders for a
                portal sub-agent, so the dollar estimate is gated on the viewer, not on the
                screen it sits on. */}
            {canSeeClaimEstimate && <div><div style={lbl}>Est. indemnity</div><div style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: (estIndemnity || 0) > 0 ? '#b3300f' : '#2d6a2d' }}>{money(estIndemnity || 0)}</div></div>}
          </div>
          {canSeeClaimEstimate && (hc.absences || []).length > 0 && (
            <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 6 }}>Not in this estimate: {hc.absences.join('; ')}.</div>
          )}
          <div style={{ fontSize: 12, color: '#4a5a4a', marginTop: 8 }}>
            {hasClaim
              ? `Production ${revShort != null && revShort > 0 && (buShort || 0) <= 0 ? 'covers bushels, but revenue' : 'falls'} short of the ${isYP ? 'yield' : 'revenue'} guarantee${harvestPx == null ? ' (bushel test only — harvest price pending)' : ''}.`
              : `Production meets the ${harvestPx == null ? 'bushel' : 'revenue'} guarantee${harvestPx != null && !harvest.isFinal ? ' at the estimated harvest price' : ''}.`}
          </div>
          {harvestPx != null && !harvest?.isFinal && (
            <div style={{ fontSize: 10.5, color: '#7a5b00', marginTop: 10 }}>Indemnity is an ESTIMATE — harvest price is a running July futures average, not the RMA final. Excludes quality/test-weight adjustments and unit-level structure.</div>
          )}
        </div>
        )}
        {/* ── THE SAME TEST, ON THE MONITOR, WHEN NOTHING IS COUNTED YET ──────
            Mutually exclusive with the block above by construction: that one needs
            production > 0, this one needs production <= 0. They can never both render,
            so a reader is never handed two revenue answers. */}
        {hasClaim == null && jdEstRevenue != null && isRevenueBasis && (
        <div style={{ ...card, gridColumn: isMobile ? 'auto' : '1 / -1', background: '#fdf9ef', borderColor: '#e8d9ae' }}>
          <div style={{ fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#7a5b00', fontWeight: 800, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            The revenue test, on the monitor
            <span style={{ fontSize: 10, background: '#fdf6e7', border: '1px solid #e8d9ae', color: '#7a5b00', padding: '2px 7px', borderRadius: 4 }}>MONITOR ESTIMATE</span>
          </div>
          <div style={{ display: 'flex', gap: isMobile ? 14 : 26, flexWrap: 'wrap' }}>
            <div><div style={lbl}>Rev. guarantee</div><div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{money(revGuar)}</div></div>
            <div><div style={lbl}>Est. revenue</div><div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{money(jdEstRevenue)}</div><div style={{ fontSize: 10.5, color: '#7a5b00' }}>{bu(jdEstBu)} {cropUnit} × {px(hp)}</div></div>
            <div><div style={lbl}>{jdEstRevenue >= revGuar ? 'Over guarantee' : 'Under guarantee'}</div><div style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: jdEstRevenue >= revGuar ? '#2d6a2d' : '#b3300f' }}>{money(Math.abs(jdEstRevenue - revGuar))}</div></div>
          </div>
          {/* The basis, on the face of it. Acres covered is printed rather than used as
              a gate — a reader who can see 60% coverage can judge it; a card hidden on a
              threshold tells them nothing at all. */}
          <div style={{ fontSize: 11.5, color: '#5a4a2a', marginTop: 10, lineHeight: 1.5 }}>
            Machine bushels over <b>{bu(jdAcresCounted)}</b> ac
            {jdEstCoverage != null && <> of <b>{bu(plantedAcres)}</b> planted ({Math.round(jdEstCoverage * 100)}%)</>}.
            {' '}This is <b>not production to count</b> — RMA counts delivered, stored, fed and saved seed, and a monitor differs
            from a scale ticket for ordinary reasons (moisture basis, calibration, grain left in the field).
            Enter production above and the counted test replaces this one.
          </div>
        </div>
        )}
      </div>
      )}
    </div>
  );
}

// crop_coverage.crop is stored UPPER (WHEAT / WINTER WHEAT / CORN / SOYBEANS); tickets +
// claim_check_inputs use title case (grainTicketCrop). Keep both mappings straight.
const CC_CROP_TOKENS = { Wheat: ['WHEAT', 'WINTER WHEAT'], Corn: ['CORN'], Soybeans: ['SOYBEANS'] };
// John Deere Ops Center crop tokens (jd_field_county.crop / jd_field_operations.crop, UPPER).
const JD_CROP = { Wheat: 'WHEAT', Corn: 'CORN', Soybeans: 'SOYBEANS' };

// ─────────────────────────────────────────────────────────────────────────────
// CLAIMS MANAGER — book-wide claims-management board (Claims page tab).
// Pick a campaign (crop-year + claim type); auto-roster every farmer with that
// coverage; track each through the pipeline (contacted → over/under → claim filed);
// auto-compute over/under from any imported scale tickets; cross-reference the Active
// Claims tab to flag anyone under-guarantee with no matching open claim. Click a row
// to open the single-farm detail (ClaimCheckView) inline.
// ─────────────────────────────────────────────────────────────────────────────
// ⚠ ONE EXPRESSION, NOT A SECOND COPY. All Customers resolves a grower's agent as
// `f._agentOverride || f.agent` and canonicalises it; this is the same read against
// the same farmer object, lifted so the two surfaces cannot drift into disagreeing
// about whose farm this is. An override always outranks the stored name.
const UNATTRIBUTED_AGENT = 'Unattributed';
const cmAgentOf = (r) => {
  const f = r && r.farmer;
  return f ? canonAgent(f._agentOverride || f.agent) : null;
};

// ── THE ADJUSTER PANEL ───────────────────────────────────────────────────────
//
// Kevin, 2026-09-28: *"I need you to act as a digital adjuster… generate what you think
// the farmer is owed"* and *"Can the super adjuster have a chat feature where we could
// ask it questions?"*
//
// One farm's prevent-plant determination, with its working shown, plus the two things an
// agent does with it: FREEZE it before the carrier pays, and ASK about it.
//
// ⚠ IT SHOWS ITS WORKING BECAUSE THE NUMBER ALONE IS NOT USABLE. An agent repeats this to
// a grower; "we think you are owed $27,304" with nothing behind it is a number they
// cannot defend on the phone. So every cell prints the acres, the eligibility and HOW it
// was established, the factor, the guarantee quantity, the price, the election and the
// share — and where acres were borrowed, which crop lent them and at whose rate.
//
// ⚠ AND IT NEVER HIDES WHAT IT COULD NOT DO. Unpriced acres, unplaced acres, an
// order-dependent total and every named absence render in their own right. A total that
// quietly excluded them would read as the whole claim.
// ── WHAT WE HOLD FOR A PRODUCTION REPORT ─────────────────────────────────────
//
// Kevin, 2026-09-30: *"Maybe display all of the data we have for production and if we agree
// with it and know it's all correct, we can hit a button to fill production report."*
//
// This is the DISPLAY. It shows every channel side by side with its RMA record-type code,
// names every disagreement, and resolves none of them. `src/claims/productionEvidence.js`
// holds the rules and decides nothing; this renders what it returns.
//
// ⚠ THERE IS NO FILL BUTTON YET AND THE PANEL SAYS SO RATHER THAN IMPLYING ONE. The form
// exists (the NAU "Actual Production History (APH) Database", whose value columns are
// calibrated), but the Dropbox `production` ladder has no `ready_for_silas` / `silas_filled`
// rungs and `stageFillable` still refuses production reports by regex. Shipping a button
// that cannot stage anything would be worse than shipping the review on its own — the
// review is useful today, on 104 outstanding CY2026 wheat farms.
//
// ⚠⚠ SEEN RENDERED — AND THE CLAIM THAT IT COULD NOT BE WAS WRONG, WHICH IS THE HALF
// WORTH RECORDING. This block previously said the modal opens from `<div onClick>` /
// `<tr onClick>` rows "while `--click` reaches only BUTTONS", so the harness could not get
// to it. `--click` tries `button` first and then FALLS BACK to `div,li,tr,td,a,span`
// (shortest match wins), and a leading `=` makes the match exact. The panel was reachable
// the whole time; what was missing was reading the tool rather than assuming its shape.
//
//   node tools/check-screens.js --screens=claims --origin=http://localhost:5950 \
//     --click="Claim Manager;Wheat Harvest;ANDERSON FARMS DAIRY" --shot=<dir>
//
// All three clicks land and the page reads 26,677 chars against a 7,656 baseline — the
// modal is open. ANDERSON FARMS DAIRY LLC is the drill-in worth using: it is the only
// CY2026 wheat farm carrying THREE counties and scale tickets (26,541 bu over 21 loads), so
// the per-county table has something to be wrong about.
//
// ⚠ A full-page shot is NOT enough here and that is a reusable fact. The overlay is
// `position: fixed`, so `captureBeyondViewport` renders the ROSTER behind everything below
// the viewport line rather than the rest of the card — the modal appears to end mid-way. A
// tall `Emulation.setDeviceMetricsOverride` before the clicks is what makes the whole card
// paint. check-screens does not do that today, so a visual check of anything inside this
// modal needs its own run.
//
// ⚠⚠ AND RENDERING IT IS WHAT FOUND THE SHRINK DEFECT. Every static check was green and
// 87 assertions passed while this panel printed 26,541 bu — the ELEVATOR's paid net — eight
// inches below a block already printing 26,911 and explaining the difference. Nothing but
// looking at the two numbers on one screen would have surfaced it. See the note on
// `rmaNetFromTickets` in productionEvidence.js for the measurement.
// ── THE FILL BUTTON ──────────────────────────────────────────────────────────
//
// Kevin, 2026-10-01: *"I dont see a button to fill production report form"*, and then
// 2026-10-02: *"Do the production report fill button."*
//
// ⚠ THE REPORT IS FILED PER UNIT AND EVERYTHING ABOVE THIS TOTALS PER COUNTY. The NAU
// "Actual Production History (APH) Database" pre-prints one block per unit — crop, plan,
// unit number, county, practice, share, acres — with three blanks per block: Yield Per
// Acre, Total Production, Record Type. So the card above answers "which production", and
// this answers "which unit", and those are different questions. `buildProductionUnits`
// splits per unit from the machine data; where it cannot, the unit is left visibly blank
// with its reason rather than taking the county total, because a county figure written
// into one unit's row overstates that unit and silently reports nothing for the others.
//
// ⚠ THE FORM DECIDES WHICH UNITS EXIST, NOT US. It is pre-printed by the carrier, so a
// unit our book knows that the form does not list has nowhere to go — it is REPORTED
// rather than dropped, because a short report that reads as a complete one is the costly
// failure on a document an AIP settles a claim against.
//
// ⚠ AND A GROWER'S OWN FIGURE IS NEVER REPLACED. If a block already carries production it
// is left alone — refused here AND by the stamper, so neither gate is the only one.
//
// The preview IS the gate: an agent reads it, then presses fill. Both actions run ONE
// resolve on the server, so the figures shown are the figures written.
function ProductionFillPanel({ farmer, crop, cropYear, basis, qty }) {
  const [st, setSt] = React.useState({ phase: "idle", err: null, d: null, busy: false });
  // Reset on every farm / crop / year / basis change. A preview computed against the SCALE
  // basis is not a statement about the MONITOR basis, and carrying it over would leave a
  // fill button whose figures came from a choice the reader has since changed.
  React.useEffect(() => { setSt({ phase: "idle", err: null, d: null, busy: false }); },
    [farmer.id, crop, cropYear, basis]);

  // ⚠ NO `basis` IS SENT (2026-10-18). The server fills from the yield monitor ONLY and never
  // read the field, so sending the card's choice made the fill look as if it followed it. The
  // basis actually used comes back as d.basisLabel and is printed; a different choice on the
  // card above is named beside it rather than silently ignored.
  // `formId` is sent only when the agent picked one of several staged forms; otherwise the
  // server finds the staged form itself.
  async function call(action, formId) {
    setSt((s) => ({ ...s, busy: true, err: null }));
    try {
      const pick = formId || (st.d && st.d.form && st.d.form.documentId) || undefined;
      const r = await fetch(`/api/market?fn=production-fill&action=${action}`, {
        method: "POST", headers: { ...getApiAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ farmer_id: farmer.id, crop, crop_year: cropYear, form_document_id: pick }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.error === "string" ? d.error : `HTTP ${r.status}`);
      setSt({ phase: action, err: null, d, busy: false });
    } catch (e) {
      setSt((s) => ({ ...s, busy: false, err: e.message }));
    }
  }

  // The production folder may simply never have been staged into before — offering to
  // create it is the difference between a dead end and a next step.
  // ⚠ THE FOLDER COMES FROM THE SERVER'S OWN LOOKUP (d.stageFolder). This used to send
  // `folder_year: cropYear` and no season, which `resolveDocRoot` refuses ("year and season are
  // required") — and a production report for crop year Y lives in the Y+1 renewal folder.
  async function makeFolder() {
    const sf = st.d && st.d.stageFolder;
    if (!sf || !sf.folderYear) return;
    setSt((s) => ({ ...s, busy: true, err: null }));
    try {
      const r = await fetch("/api/market?fn=dropbox&action=ensure-stage-folder", {
        method: "POST", headers: { ...getApiAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ doc: "production", folder_year: String(sf.folderYear), season: sf.season }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) throw new Error(d.error || `HTTP ${r.status}`);
      setSt((s) => ({ ...s, busy: false, err: null }));
      await call("preview");
    } catch (e) { setSt((s) => ({ ...s, busy: false, err: e.message })); }
  }

  const d = st.d;
  const unitLabel = (d && d.units && d.units.unit) || null;
  const btn = (label, onClick, kind) => (
    <button type="button" onClick={onClick} disabled={st.busy}
      style={{
        padding: "7px 13px", fontSize: 12.5, fontWeight: 700, borderRadius: 6,
        cursor: st.busy ? "wait" : "pointer",
        border: kind === "go" ? "none" : "1px solid #cfdccf",
        background: st.busy ? "#e8eee8" : kind === "go" ? "#2d6a2d" : "#fff",
        color: kind === "go" ? "#fff" : "#1a2e1a",
      }}>{st.busy ? "Working…" : label}</button>
  );

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #dfe8df" }}>
      {st.phase === "idle" && !st.busy && (
        <>
          {btn("Check the production report form", () => call("preview"))}
          <div style={{ fontSize: 11.5, color: "#6a7a6a", marginTop: 6 }}>
            Reads the report staged in this crop year&apos;s production folder under SIGNED
            &amp; READY / Ready for Silas, and shows what would go on each unit before
            anything is written.
          </div>
        </>
      )}
      {st.phase === "idle" && st.busy && (
        <div style={{ fontSize: 12, color: "#6a7a6a" }}>Reading the staged form…</div>
      )}

      {st.err && (
        <div style={{ fontSize: 12, color: "#8a2d2d", marginTop: 8 }}>Couldn&apos;t do that: {st.err}</div>
      )}

      {d && (
        <div style={{ marginTop: 8 }}>
          {/* WHICH DOCUMENT. A fill that does not name what it wrote on is unauditable. */}
          {d.form ? (
            <div style={{ fontSize: 12, color: "#1a2e1a" }}>
              <strong>{d.form.name || "staged form"}</strong>
              {d.form.dropboxPath && <span style={{ color: "#6a7a6a" }}> · {d.form.dropboxPath}</span>}
              {d.form.filledAt && (
                <span style={{ color: "#8a6d1f" }}> · already filled once, {new Date(d.form.filledAt).toLocaleDateString()}</span>
              )}
            </div>
          ) : (d.choices || []).length > 1 ? (
            <>
              {/* SEVERAL STAGED FORMS: the agent picks. Nothing is guessed — filling the wrong
                  one puts this crop's figures on another policy's report. */}
              <div style={{ fontSize: 12, color: "#1a2e1a" }}>
                {d.choices.length} production reports are staged for this farmer. Pick the one for {crop}:
              </div>
              <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {d.choices.map((c) => (
                  <React.Fragment key={c.id}>{btn(c.name || "staged form", () => call("preview", c.id))}</React.Fragment>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* ⚠ THE SERVER OWNS THIS SENTENCE, both here and on the fill path, because it is
                  the one that says WHERE to stage the report. A local fallback here was true and
                  useless — it told the reader nothing is staged and left them with no next step. */}
              <div style={{ fontSize: 12, color: "#8a6d1f" }}>
                {d.stageError ? `Could not look in Ready for Silas: ${d.stageError}` : (d.notStaged || d.error ||
                  "Nothing is staged for this farmer, and the server sent no reason — it may be an older deploy.")}
              </div>
              {d.stagedIn && (
                <div style={{ fontSize: 11.5, color: "#6a7a6a", marginTop: 2 }}>Looked in: {d.stagedIn}</div>
              )}
              {d.stageFolder && d.stageFolder.folderYear && !d.stageError && (
                <div style={{ marginTop: 6 }}>{btn("Create the Ready for Silas folder", makeFolder)}</div>
              )}
            </>
          )}
          {d.formRefused && (
            <div style={{ fontSize: 12, color: "#8a2d2d", marginTop: 4 }}>{d.formRefused}</div>
          )}
          {d.otherCropBlocks > 0 && (
            <div style={{ fontSize: 11.5, color: "#6a7a6a", marginTop: 4 }}>
              {d.otherCropBlocks} block{d.otherCropBlocks === 1 ? " is" : "s are"} for another crop and will not be touched.
            </div>
          )}
          {d.formError && (
            <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
              The staged form could not be read: {d.formError}
            </div>
          )}
          {/* ⚠ A BLOCK THE READER COULD NOT PARSE IS NAMED, not skipped — a unit missing from
              this list is a unit nothing will be written for, and silence reads as absence. */}
          {!!(d.form && (d.form.unresolved || []).length) && (
            <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
              {d.form.unresolved.length} block{d.form.unresolved.length === 1 ? "" : "s"} on the form
              could not be read, so nothing will be written there: {d.form.unresolved.join("; ")}
            </div>
          )}

          {/* WHICH BASIS these figures came from, restated here because this is the panel that
              writes them. The card above offers the choice; the fill must not look independent
              of it. */}
          {d.basisLabel && (
            <div style={{ fontSize: 11.5, color: "#5a6a5a", marginTop: 4 }}>
              Per-unit figures from {d.basisLabel}{d.basisWhy ? ` — ${d.basisWhy}` : ""}
            </div>
          )}
          {/* ⚠ THE FILL USES THE YIELD MONITOR ONLY. If the card above is set to another basis,
              say so here — the fill does not follow that choice. */}
          {d.basis && basis && basis !== d.basis && (
            <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
              The card above is set to a different basis. This form is filled from the yield
              monitor only; that choice does not change what is written here.
            </div>
          )}
          {d.minCoverage != null && (
            <div style={{ fontSize: 11.5, color: "#5a6a5a", marginTop: 2 }}>
              A unit is filled only where the combine covered at least {Math.round(d.minCoverage * 100)}% of its insured acres.
            </div>
          )}

          {/* ── PER UNIT ── */}
          {!!(d.lines || []).length && (
            <div style={{ overflowX: "auto", marginTop: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ textAlign: "left", color: "#5a6a5a" }}>
                  <th style={{ padding: "4px 8px 4px 0" }}>Unit</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Acres (form)</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Acres (ours)</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Yield / ac</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Production</th>
                  <th style={{ padding: "4px 8px" }}>Type</th>
                  <th style={{ padding: "4px 0" }}>Status</th>
                </tr></thead>
                <tbody>
                  {d.lines.map((l, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #eef3ee" }}>
                      <td style={{ padding: "6px 8px 6px 0", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {l.unitNumber}
                        {l.county && <span style={{ fontWeight: 400, color: "#6a7a6a" }}> · {l.county}</span>}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {l.formAcres == null ? "—" : l.formAcres.toLocaleString()}
                      </td>
                      {/* ⚠ BOTH ACREAGES SIDE BY SIDE, RECONCILED NOWHERE. This is the pair that
                          caught an SCO/ECO double-count where our acreage read twice the form's,
                          and the only reason it was caught is that both were on screen. The
                          disagreement is a flag for a human, not a refusal: the form states its
                          own acreage and the production was measured independently of it. */}
                      <td style={{
                        padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                        color: l.acreageAgrees === false ? "#8a6d1f" : "#1a2e1a",
                        fontWeight: l.acreageAgrees === false ? 700 : 400,
                      }}>
                        {!l.ours || l.ours.insuredAcres == null ? "—" : l.ours.insuredAcres.toLocaleString()}
                        {l.acreageAgrees === false ? " ⚠" : ""}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {!l.ours || l.ours.yieldPerAcre == null ? "—" : l.ours.yieldPerAcre.toFixed(1)}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: l.write ? 700 : 400 }}>
                        {l.alreadyStated && l.formQuantity != null
                          ? <span style={{ color: "#6a7a6a" }}>{qty(l.formQuantity, unitLabel)} (theirs)</span>
                          : !l.ours || l.ours.quantity == null ? "—" : qty(l.ours.quantity, unitLabel)}
                      </td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {l.alreadyStated ? (l.formRecordType || "—") : (!l.ours ? "—" : l.ours.recordType || "—")}
                      </td>
                      {/* A REFUSAL CARRIES ITS REASON ON ITS OWN ROW. A blank cell with no
                          sentence beside it reads as a broken page rather than as a decision. */}
                      <td style={{ padding: "6px 0", color: l.write ? "#2d6a2d" : "#8a6d1f", fontWeight: l.write ? 700 : 400 }}>
                        {l.write ? "will fill" : (l.why || "not filled")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ⚠ A UNIT WE INSURE THAT THE FORM DOES NOT LIST. There is nowhere to write it, so
              it is named — otherwise the filled form reads as covering the whole crop. */}
          {!!(d.notOnForm || []).length && (
            <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 6 }}>
              ⚠ {d.notOnForm.length} unit{d.notOnForm.length === 1 ? " we insure is" : "s we insure are"} not
              pre-printed on this form, so nothing can be written for{" "}
              {d.notOnForm.length === 1 ? "it" : "them"}:{" "}
              {d.notOnForm.map((u) => `${u.unitKey}${u.county ? ` (${u.county})` : ""}`).join(", ")}.
              This form covers part of the crop.
            </div>
          )}

          {/* ── THE FILL ── */}
          {d.action !== "fill" && d.form && (
            <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {d.willWrite > 0
                ? btn(`Fill ${d.willWrite} unit${d.willWrite === 1 ? "" : "s"} and file it`, () => call("fill"), "go")
                : <div style={{ fontSize: 12, color: "#8a6d1f" }}>Nothing on this form can be filled — see the per-unit reasons above.</div>}
              {d.refused > 0 && d.willWrite > 0 && (
                <span style={{ fontSize: 11.5, color: "#8a6d1f" }}>{d.refused} left blank</span>
              )}
            </div>
          )}

          {/* ── WHAT ACTUALLY HAPPENED ── Every half that can fail reports separately: one
              boolean cannot say which of three writes did not happen. */}
          {d.action === "fill" && (
            <div style={{ marginTop: 10, padding: 10, background: d.ok ? "#f1f7f1" : "#fdf6ec", borderRadius: 6 }}>
              {d.ok ? (
                <>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2e1a" }}>
                    Filled {(d.stamped || []).length} unit{(d.stamped || []).length === 1 ? "" : "s"}
                    {d.placed ? " and filed it into Silas Filled" : ""}
                  </div>
                  {d.url && (
                    <div style={{ marginTop: 6 }}>
                      <a href={d.url} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12.5, color: "#2d6a2d", fontWeight: 700 }}>Open the filled form</a>
                    </div>
                  )}
                  {/* ⚠ THE STAMPER'S REFUSALS ARE A DIFFERENT SET FROM THE RESOLVER'S. It
                      refuses what the FORM cannot carry; the resolver refuses what the
                      EVIDENCE cannot say. Both are shown, because they have different fixes. */}
                  {!!(d.stamperRefused || []).length && (
                    <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 6 }}>
                      The form itself would not take {d.stamperRefused.length}:{" "}
                      {d.stamperRefused.map((r) => `${r.unitNumber} — ${r.why}`).join("; ")}
                    </div>
                  )}
                  {d.placeError && (
                    <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
                      ⚠ Not filed into Dropbox: {d.placeError}. The filled copy above is still saved.
                    </div>
                  )}
                  {d.sourceArchiveError && (
                    <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
                      ⚠ The staged original is still in Ready for Silas ({d.sourceArchiveError}) — move
                      it by hand, or the next run will fill it again.
                    </div>
                  )}
                  {d.rowError && (
                    <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
                      ⚠ The document record was not stamped as filled ({d.rowError}), so the renewal
                      board may still show this one as outstanding.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12.5, color: "#8a6d1f" }}>{d.error || "Nothing was written."}</div>
                  {/* A fill that stamped the PDF but could not file it into Dropbox still saved
                      the copy; it is linked so the work is not lost, and NOT marked filled. */}
                  {d.url && (
                    <div style={{ marginTop: 6 }}>
                      <a href={d.url} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12.5, color: "#2d6a2d", fontWeight: 700 }}>Open the unfiled copy</a>
                    </div>
                  )}
                </>
              )}
              <div style={{ marginTop: 8 }}>{btn("Check again", () => call("preview"))}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── JOHN DEERE ROWS THAT MAY COUNT AS EVIDENCE ───────────────────────────────
// jd_field_ftf carries no status of its own, so a pass Deere stopped returning and a pass
// recorded twice were both counted as production. One rule, src/claims/jdEvidence.js,
// shared with the server. SANDBOX passes are KEPT (for some farms they are the only
// machine record) and tagged. Hand-imported yield maps have no operation row and are
// always kept. jd_field_operations is admin-read: where the status read is refused the
// rows stay and are counted as unverified, never dropped. `cache` (a Map) lets one panel
// apply the rule twice for one status read.
function jdEvidenceClient(rows, cache = null) {
  return jdEvidenceOnly(rows || [], (ids) => sbFetch("jd_field_operations", {
    select: JD_EVIDENCE_STATUS_COLUMNS.join(","), filters: [`jd_operation_id=in.(${ids.join(",")})`] }), null, cache);
}
// What was left out, and what was counted with a caveat — said once. Renders nothing when
// there is nothing to say. The both-hosts flag is ADMIN-only: it is a question for us
// about our data, not a statement a grower can act on.
function JdEvidenceNote({ report, opsReport = null, season = null }) {
  if (!report) return null;
  const n = (k) => (report[k] ? report[k].rows : 0) + (opsReport && opsReport[k] ? opsReport[k].rows : 0);
  const ac = (k) => (report[k] ? report[k].acres : 0) + (opsReport && opsReport[k] ? opsReport[k].acres : 0);
  const parts = [];
  if (n("duplicate")) parts.push(`${ac("duplicate").toFixed(1)} ac recorded twice`);
  if (n("gone")) parts.push(`${ac("gone").toFixed(1)} ac John Deere no longer returns`);
  const sbx = n("sandbox") ? `${ac("sandbox").toFixed(1)} ac from John Deere's test host (sandbox) — counted; not yet confirmed as this farm's own record.` : null;
  const both = [...(report.bothHosts || []), ...((opsReport && opsReport.bothHosts) || [])]
    .some((b) => season == null || String(b.season) === String(season));
  const showBoth = both && isCurrentUserAdmin();
  const unv = report.unverified ? report.unverified.rows : 0;
  if (!parts.length && !unv && !sbx && !showBoth) return null;
  return (
    <div style={{ fontSize: 11.5, color: "#7a5a00", background: "#fff8e6", border: "1px solid #f0dca8", borderRadius: 8, padding: "6px 10px", marginBottom: 10 }}>
      {parts.length > 0 && <div>Not counted as production: {parts.join(" · ")}.</div>}
      {sbx && <div>{sbx}</div>}
      {showBoth && <div style={{ fontWeight: 700 }}>Possible double count — both John Deere hosts report work this season.</div>}
      {unv > 0 && <div>{unv} John Deere row{unv === 1 ? "" : "s"} could not be checked and {unv === 1 ? "is" : "are"} still counted.</div>}
    </div>
  );
}

// ⚠ THE FARM-LEVEL ACREAGE ANSWER, AS A FUNCTION so the panel's effect (which reports it up to
// ClaimCheckView before any early return) and its render call the same code. The arithmetic is
// acreAccounting() in src/claims/fieldCoverage.js and nothing here adds to it.
function panelAcreDenom(ev, fc) {
  if (!ev || !fc) return acreAccounting({ totals: {} });
  const policyInsured = +((ev.counties || []).reduce((a, c) => a + (c.insuredAcres || 0), 0)).toFixed(1);
  const policyPp = +((ev.counties || []).reduce((a, c) => a + (c.ppAcres || 0), 0)).toFixed(1);
  const countyAppraised = +((ev.counties || [])
    .reduce((a, c) => a + ((c.appraised && c.appraised.acres) || 0), 0)).toFixed(1);
  const acc = acreAccounting({ totals: { ...fc.totals, insuredAcres: acreBasis(fc).insuredAcres || 0 },
                               policyInsured, policyPp, countyAppraised });
  return { ...acc, policyInsured, policyPlanted: acc.denom, pp: policyPp };
}

// ── YIELD PER ACRE BY UNIT ────────────────────────────────────────────────────────────────
// One row per insurance unit: its yield (bushels ÷ the planted acres those bushels stand for)
// beside its guarantee per acre. Every figure is buildUnitYield (src/claims/unitYield.js); this
// only draws it. Red ONLY when 90%+ of the unit is harvested and the yield is under the guarantee
// (Kevin, 2026-10-31: flag the whole unit, never a field; 2026-11-03: "Fully harvested over 90%").
function UnitYieldBlock({ uy, open, setOpen, ac1, hasTickets }) {
  const cell = { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const head = { ...cell, fontSize: 10, color: "#60735f", fontWeight: 700 };
  const bu0 = (v) => (v > 0 ? Math.round(v).toLocaleString() : "—");
  const y1 = (v) => (v == null ? "—" : v.toFixed(1));
  const multiPolicy = new Set(uy.units.map((u) => u.policyNumber)).size > 1;
  const sum = (list) => list.reduce((a, f) => a + (f.bu || 0), 0);
  const offBu = sum(uy.fieldsOffUnit || []), noAcBu = sum(uy.fieldsNoAcres || []);
  const unplBu = sum(uy.unplacedOffUnit || []);
  const fieldsTxt = (list) => list.slice(0, 4).map((f) => f.field).join(", ") + (list.length > 4 ? ` +${list.length - 4} more` : "");
  if (!uy.units.length) {
    return (
      <div style={{ borderTop: "1px solid #eef3ee", marginTop: 11, paddingTop: 11, fontSize: 11.5, color: "#60735f" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5a6a5a", fontWeight: 700, marginBottom: 4 }}>Yield per acre by unit</div>
        The carrier's unit list for this crop isn't on file, so yield can't be shown by unit yet.
      </div>
    );
  }
  return (
    <div style={{ borderTop: "1px solid #eef3ee", marginTop: 11, paddingTop: 11 }}>
      <div style={{ fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5a6a5a", fontWeight: 700, marginBottom: 6 }}>
        Yield per acre by unit
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto auto", gap: "5px 12px", fontSize: 12, alignItems: "baseline" }}>
        <div />
        <div style={head}>Yield</div>
        <div style={head}>Guarantee</div>
        <div style={head}>Difference</div>
        {uy.units.map((u) => {
          const S = u.status;
          const red = S === UY_STATUS.BELOW, green = S === UY_STATUS.AT_OR_ABOVE;
          const yColor = red ? "#b3300f" : green ? "#2d6a2d" : "#4a5a4a";
          const isOpen = !!open[u.key];
          const diff = S === UY_STATUS.BELOW || S === UY_STATUS.AT_OR_ABOVE
            ? <span style={{ fontWeight: 700, color: yColor }}>{u.vsGuarantee > 0 ? "+" : ""}{u.vsGuarantee.toFixed(1)}</span>
            : <span style={{ fontSize: 10.5, color: "#7a6a2a" }}>{
                S === UY_STATUS.SO_FAR ? "so far"
                : S === UY_STATUS.TOO_FEW ? "too few ac"
                : S === UY_STATUS.SILAGE ? "silage, in tons"
                : S === UY_STATUS.NO_GUARANTEE ? "no guarantee"
                : "no figure yet"}</span>;
          return (
            <React.Fragment key={u.key}>
              <div style={{ minWidth: 0, borderTop: "1px solid #f0f4f0", paddingTop: 5 }}>
                <div style={{ fontWeight: 800, color: "#1a2e1a" }}>
                  Unit {u.unit}{u.county ? <span style={{ fontWeight: 400, color: "#5a6a5a" }}> · {u.county}</span> : null}
                  {multiPolicy && u.policyNumber && <span style={{ fontWeight: 400, color: "#60735f", fontSize: 10.5, marginLeft: 6 }}>{u.policyNumber}</span>}
                </div>
                <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 1 }}>
                  {S === UY_STATUS.SILAGE ? `${ac1(u.plantedAcres)} ac insured as silage`
                    : `${bu0(u.bu)} bu on ${ac1(u.acres)} of ${ac1(u.plantedAcres)} planted ac`}
                  {u.fields.length > 0 && (
                    <button type="button" onClick={() => setOpen((o) => ({ ...o, [u.key]: !o[u.key] }))}
                      style={{ marginLeft: 8, border: "none", background: "none", padding: 0, color: "#2d6a2d", fontSize: 10.5, fontWeight: 700, cursor: "pointer" }}>
                      {isOpen ? "Hide fields" : `Show ${u.fields.length} field${u.fields.length === 1 ? "" : "s"}`}
                    </button>
                  )}
                </div>
              </div>
              <div style={{ ...cell, borderTop: "1px solid #f0f4f0", paddingTop: 5, fontWeight: 800, fontSize: 13, color: yColor }}>{y1(u.yield)}</div>
              <div style={{ ...cell, borderTop: "1px solid #f0f4f0", paddingTop: 5, color: "#4a5a4a" }}
                title={u.guaranteePerAcre == null ? (u.guaranteeWhy || "") : u.guaranteeBasis === "stated" ? "Guarantee per acre as the carrier states it" : "APH × coverage level"}>
                {y1(u.guaranteePerAcre)}</div>
              <div style={{ ...cell, borderTop: "1px solid #f0f4f0", paddingTop: 5 }}>{diff}</div>
              {isOpen && u.fields.map((f) => (
                <React.Fragment key={u.key + f.field + (f.cluId || "")}>
                  <div style={{ minWidth: 0, paddingLeft: 12, fontSize: 11, color: "#5a6a5a" }}>
                    Field {f.field}
                    <span style={{ color: "#60735f", fontSize: 10, marginLeft: 6 }}>
                      {f.bu > 0 ? `${bu0(f.bu)} bu on ${ac1(f.acres)} of ${ac1(f.plantedAcres)} ac${f.source ? " · " + f.source : ""}` : `${ac1(f.plantedAcres)} ac · no figure yet`}
                    </span>
                  </div>
                  <div style={{ ...cell, fontSize: 11, color: "#4a5a4a" }}>{f.bu > 0 ? y1(f.yield) : ""}</div>
                  <div /><div />
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ fontSize: 10.5, color: "#60735f", lineHeight: 1.45, marginTop: 8 }}>
        Yield is bushels over the acres they came off. Red only when 90%+ of the unit is harvested and it's under the guarantee. Under 2 acres shows bushels only.
        {noAcBu > 0 && <div style={{ marginTop: 3 }}>{bu0(noAcBu)} bu came off ground the acreage report shows no planted acres on (field {fieldsTxt(uy.fieldsNoAcres)}). Kept out of every unit yield, still in the county total.</div>}
        {offBu > 0 && <div style={{ marginTop: 3 }}>{bu0(offBu)} bu are on fields not reported on any unit (field {fieldsTxt(uy.fieldsOffUnit)}). Still in the county total.</div>}
        {unplBu > 0 && <div style={{ marginTop: 3 }}>{bu0(unplBu)} appraised bu name no single unit. Still in the county total.</div>}
        {hasTickets && <div style={{ marginTop: 3 }}>Scale tickets are whole-farm, so they aren't split by unit.</div>}
      </div>
    </div>
  );
}

// Crops whose Claims page shows "Yield per acre by unit". Soybeans first (Kevin, 2026-11-03);
// checked unit by unit against the county card on every soybean farm before it went in.
const UNIT_YIELD_CROPS = ["SOYBEANS"];
function ProductionEvidencePanel({ farmer, crop, cropYear, isMobile, claimRead, onAccounting }) {
  const [st, setSt] = React.useState({ loading: true, err: null, ev: null });
  // WHICH production would be filed. null = take the module's default and say so.
  // Reset whenever the crop/year changes: a basis chosen for one crop is not a
  // statement about another, and carrying it over would be a silent decision.
  const [basis, setBasis] = React.useState(null);
  // The unaccounted-for fields are collapsed by default — see the table below. Not persisted:
  // it is a per-reading convenience, and a sticky "show everything" would put the wall of
  // dashes back in front of the next farm somebody opens.
  const [showAllFields, setShowAllFields] = React.useState(false);
  // Which units have their fields open in "Yield per acre by unit" (keyed by unit key).
  const [openUnits, setOpenUnits] = React.useState({});
  // High-moisture corn switch (admin only): a reload counter, the field being saved, and a save error.
  const [hmcTick, setHmcTick] = React.useState(0);
  const [hmcBusy, setHmcBusy] = React.useState(null);
  const [hmcErr, setHmcErr] = React.useState(null);
  React.useEffect(() => { setBasis(null); setShowAllFields(false); setOpenUnits({}); }, [farmer.id, crop, cropYear]);
  // Report the planted acres with no production figure UP, so the scaled-yield figure in
  // ClaimCheckView reads the SAME answer as this page's headline instead of its own.
  React.useEffect(() => {
    if (!onAccounting) return;
    if (!st.ev || !st.fc) { onAccounting(null); return; }
    const v = claimAccountingView({ fcRows: st.fc.rows, counties: st.ev.counties || [], farm: panelAcreDenom(st.ev, st.fc), farmLevel: st.fc.farmLevelAppraisals, unplaced: st.fc.unplacedAppraisals });
    // GRAIN acres (Kevin, 2026-11-03: the insured type decides): silage-insured acres waiting on
    // tons are the silage line's status and never hold the grain "so far" or the yield gate.
    onAccounting({ stillNeed: v.total.grainNothing, planted: v.total.grainPlanted, lines: v.lines, mode: v.mode, total: v.total,
      hmcKeys: st.fc.rows.filter((r) => r.hmcMarked).map((r) => r.cluId),
      // The grain combine figure the claim verdict counts (computeHarvestClaim `combine`) — the
      // same rows as this page's "Combined" line, so the two can never disagree.
      combine: grainCombineOf(st.fc) });
  }, [st.ev, st.fc]);   // eslint-disable-line

  React.useEffect(() => {
    let alive = true;
    // A reload after an HMC switch keeps the page (and the open Details) instead of blanking it.
    setSt((prev) => (hmcTick > 0 && prev && prev.ev ? prev : { loading: true, err: null, ev: null }));
    (async () => {
      try {
        const pols = await sbFetch("policies", {
          select: "id,policy_number,crop_year,farmer_id",
          filters: [`farmer_id=eq.${farmer.id}`, `crop_year=eq.${cropYear}`],
        });
        if (!pols.length) { if (alive) setSt({ loading: false, err: null, ev: null }); return; }
        const inList = `(${pols.map((p) => p.id).join(",")})`;
        const [cov, tickets, jdFtf, policyFieldRows, appraisals, countyRows, inputs, aphRows,
               jdOps, hmcRows] = await Promise.all([
          sbFetch("crop_coverage", { select: "policy_id,crop,county,acres,pp_acres,ui_acres,mpci_plan_code,type_code",
            filters: [`policy_id=in.${inList}`] }),
          sbFetch("grain_delivery_tickets", { select: "crop,crop_year,net_bushels,gross_bushels,moisture",
            filters: [`farmer_id=eq.${farmer.id}`, `crop_year=eq.${cropYear}`] }),
          // clu_id rides along because it is what scopes the machine acres to INSURED
          // ground — see the jd lane in productionEvidence.js. A column the rule reads
          // and the query never asked for is the dead-guard shape this repo keeps paying for.
          // ⚠ jd_field_id / jd_operation_id ride along for the UNMAPPED-PASS half. The first
          // ties a CLU to the John Deere field an ungridded pass belongs to; the second is how
          // a pass that DID build its map is recognised and not counted twice. Omit either and
          // every ungridded pass falls to `unplacedPasses` — the rule runs, places nothing,
          // and looks exactly like a book with no ungridded passes in it.
          sbFetch("jd_field_ftf", { select: "county,acres,mean_value,crop,operation_date,metric,clu_id,jd_field_id,jd_operation_id",
            filters: [`farmer_id=eq.${farmer.id}`] }),
          // Which CLUs this farm insures FOR THIS CROP this year. Scoped by policy, not by
          // farmer_id, because policy_fields carries both and the policy set is already the
          // crop-year scope everything else on this panel uses.
          // ⚠ fsn / tract / field_id / acres ride along for buildFieldCoverage — see
          // NEEDS_COLUMNS in fieldCoverage.js. Without `tract` every appraisal looks like a
          // no-tract row and the full key never fires; without `acres` the acreage tier, which
          // places 11 of the book's 20 appraisal lines, places nothing at all.
          // ⚠ policy_id rides along for "Yield per acre by unit": a field reaches its unit by policy
          // AND unit number (UNIT_YIELD_NEEDS in unitYield.js) — without it no field reaches a unit.
          sbFetch("policy_fields", { select: "policy_id,clu_id,crop,crop_year,fsn,tract,field_id,acres,pp_acres,county,insured_county,unit_number,sub_unit,type_code",
            filters: [`policy_id=in.(${pols.map((p) => p.id).join(",")})`] }).catch(() => []),
          // fsn / tract / field_no / field_name are what tie an appraisal to an insured field —
          // NEEDS_COLUMNS in fieldCoverage.js. `field_name` is not decoration: on every live row
          // it holds the adjuster's worksheet line number, which is the evidence that their
          // `field_no` is NOT an FSA field id.
          sbFetch("silage_appraisals", { select: "crop,crop_year,county,appraised_acres,appraised_bu,appraised_qty,fsn,unit_number,type_code,tract,field_no,field_name",
            filters: [`farmer_id=eq.${farmer.id}`, `crop_year=eq.${cropYear}`] }),
          // ⚠ A TABLE A MIGRATION MAY NOT HAVE CREATED MUST NOT 500 THE PANEL. Same rule the
          // FSA-426 panel follows for its own tracking table: degrade and say so.
          sbFetch("claim_county_production", { select: "crop,crop_year,county,bushels,uom,source",
            filters: [`farmer_id=eq.${farmer.id}`, `crop_year=eq.${cropYear}`] }).catch(() => []),
          sbFetch("claim_check_inputs", { select: "crop,crop_year,stored_bu,fed_bu,seed_bu,shrink_per_pt,base_moisture",
            filters: [`farmer_id=eq.${farmer.id}`, `crop_year=eq.${cropYear}`] }).catch(() => []),
          sbFetch("production_history", { select: "crop,crop_year,county,acres,production,record_type",
            filters: [`farmer_id=eq.${farmer.id}`, `crop_year=eq.${cropYear}`] }).catch(() => []),
          // ⚠ THE COMBINE PASSES THEMSELVES, not their maps. A harvest whose yield grid failed
          // to build has no jd_field_ftf row at all, so it reads as though the combine had
          // never been there — which is what sent Kevin looking for a ripped-up wheat field
          // that did not exist. The operation states its own acres and yield; see the note on
          // `unmappedPass` in fieldCoverage.js for why those bushels are NOT split per field.
          sbFetch("jd_field_operations", {
            select: "jd_operation_id,jd_field_id,field_name,crop,operation_start,area_acres,dry_yield,yield_unit,moisture_pct,source_present,is_duplicate,source_host",
            filters: [`farmer_id=eq.${farmer.id}`, "operation_type=eq.harvest",
                      `operation_start=gte.${cropYear - 1}-07-01`, `operation_start=lt.${cropYear + 1}-01-01`],
          }).catch(() => []),
          // ⚠ HIGH-MOISTURE-CORN SWITCHES ARE AN AGENT'S DECISION AND ARE READ ONLY FOR AN ADMIN. A
          // portal user never fetches this table (and RLS would refuse it anyway); a missing table
          // (migration not applied) degrades to "nothing marked" instead of crashing the page.
          isCurrentUserAdmin()
            ? sbFetch("claim_hmc_fields", { select: "field_key,set_by_label,set_at",
                filters: [`farmer_id=eq.${farmer.id}`, `crop_year=eq.${cropYear}`,
                          `crop=eq.${encodeURIComponent(String(crop).toUpperCase())}`] }).catch(() => [])
            : Promise.resolve([]),
        ]);
        // ⚠ ONLY EVIDENCE-GRADE JOHN DEERE ROWS — see jdEvidenceClient. Both the CLU split and
        // the unmapped passes, so a gone or duplicate pass cannot re-enter as an "ungridded" one.
        const jdEvCache = new Map();
        const jdEv = await jdEvidenceClient(jdFtf || [], jdEvCache);
        const jdOpsEv = filterJdEvidenceOps(jdOps || []);
        // The NOTE speaks for this crop year only — the split read above is every year.
        const jdEvYear = await jdEvidenceClient((jdFtf || []).filter((r) => jdSeasonOf(r) === String(cropYear)), jdEvCache);
        // WHICH INSURED FIELDS ARE ACCOUNTED FOR, and by what — built FIRST, because it decides
        // which HMC marks actually APPLY (a stated grain type only), and only those may drop a
        // field's combine bushels from the totals below.
        const fc = buildFieldCoverage({
          crop, cropYear,
          policyFields: policyFieldRows || [], appraisals: appraisals || [], machineRows: jdEv.rows,
          machineOps: jdOpsEv.rows, hmcFields: hmcRows || [],
          // The claim page's acre split and matching (fieldCoverage.js); the Field Mapper leaves both off.
          partialHarvest: true, claimMatching: true,
        });
        const hmcApplied = fc.rows.filter((r) => r.hmcMarked).map((r) => ({ field_key: r.cluId }));
        const ev = buildProductionEvidence({
          crop, cropYear, unit: claimUnit(String(crop || "").toUpperCase(), null),
          coverages: cov || [], policies: pols,
          tickets: tickets || [], jdFtf: dropHmcRows(jdEv.rows, hmcApplied), policyFields: policyFieldRows || [], appraisals: appraisals || [],
          countyRows: countyRows || [], aphRows: aphRows || [],
          inputs: (inputs || []).find((r) => String(r.crop || "").toUpperCase() === String(crop).toUpperCase()) || null,
        });
        // ── YIELD PER ACRE BY UNIT (soybeans; Kevin, 2026-11-03: "no where in soybean claims
        // manager shows their yield per acres, lets add that"). The SAME field production this card
        // already counts (rowProduction mirrors claimAccounting's own), grouped onto the carrier's
        // units and set beside each unit's guarantee per acre. A unit read that fails degrades to
        // no block and a note, never a broken page.
        let uy = null, uyErr = null;
        if (UNIT_YIELD_CROPS.includes(String(crop || "").toUpperCase())) {
          try {
            const cu = await sbFetch("crop_coverage_units", { select: UNIT_YIELD_NEEDS.crop_coverage_units.join(","),
              filters: [`policy_id=in.${inList}`, `crop_year=eq.${cropYear}`, `crop=eq.${encodeURIComponent(String(crop).toUpperCase())}`] });
            uy = buildUnitYield({ crop, cropYear, fcRows: fc.rows, coverageUnits: cu || [], policies: pols,
              farmLevel: fc.farmLevelAppraisals, unplaced: fc.unplacedAppraisals });
          } catch (e) { uyErr = (e && e.message) || String(e); }
        }
        if (alive) setSt({ loading: false, err: null, ev, fc, uy, uyErr,
          hasTickets: (tickets || []).some((t) => String(t.crop || "").toUpperCase().startsWith(String(crop || "").toUpperCase().slice(0, 4)) && Number(t.net_bushels || t.gross_bushels) > 0),
          jdEv: jdEvYear.report, jdOpsEv: jdOpsEv.report });
      } catch (e) { if (alive) setSt({ loading: false, err: e.message, ev: null, fc: null }); }
    })();
    return () => { alive = false; };
  }, [farmer.id, crop, cropYear, hmcTick]);   // eslint-disable-line

  // ⚠ minWidth 0 + overflowWrap: a card is a grid/flex descendant and defaults to
  // min-width:auto — "never shrink below your widest content" — so one long
  // unbreakable run inside sets the card width and the card overflows its track.
  // MEASURED 2026-10-06 at a 390px viewport: grid 342 wide, card 417, everything
  // in it ending at 428. See the modal-panel comment for the same rule one level up.
  const card = { background: "#fff", border: "1px solid #dde8d8", borderRadius: 10,
    padding: isMobile ? 12 : 16, marginTop: 16, minWidth: 0, overflowWrap: "anywhere" };
  const h = { fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 8 };
  // Mark / unmark one field as high-moisture corn. A stored human decision — the moisture chip only
  // points at candidates and never calls this.
  const canMarkHmc = isCurrentUserAdmin() && String(crop || "").toUpperCase() === "CORN";
  const toggleHmc = async (r, on) => {
    setHmcBusy(r.cluId); setHmcErr(null);
    try {
      const key = String(crop).toUpperCase();
      if (on) {
        await sbUpsert("claim_hmc_fields", [{ farmer_id: farmer.id, crop_year: Number(cropYear), crop: key, field_key: r.cluId,
          set_by: getCurrentUserId() || null, set_by_label: getCurrentUserEmail() || null, set_at: new Date().toISOString() }],
          "farmer_id,crop_year,crop,field_key");
      } else {
        await sbDelete("claim_hmc_fields", `farmer_id=eq.${encodeURIComponent(farmer.id)}&crop_year=eq.${Number(cropYear)}&crop=eq.${encodeURIComponent(key)}&field_key=eq.${encodeURIComponent(r.cluId)}`);
      }
      setHmcTick((t) => t + 1);
    } catch (e) { setHmcErr("Couldn't save the high-moisture-corn switch: " + (e && e.message ? e.message : e)); }
    finally { setHmcBusy(null); }
  };
  const qty = (v, u) => (v == null ? "—" : Math.round(v).toLocaleString() + " " + (u || ""));

  if (st.loading) return <div style={{ ...card, color: "#60735f", fontSize: 13 }}>Reading production evidence…</div>;
  if (st.err) return <div style={{ ...card, color: "#a33", fontSize: 13 }}>Couldn't read production evidence: {st.err}</div>;
  if (!st.ev) return null;
  const ev = st.ev;
  // The line(s) a production report would carry for the chosen basis. ONE rule, so this
  // panel and any future filler cannot disagree about which bushels get reported.
  const rep = buildProductionReportLines(ev, basis);
  // ── WHERE THE FIGURE CAME FROM, AND THE ACRES IT COVERS ─────────────────────
  // Kevin, 2026-10-03: *"I just want to see clearly insured acres and JD harvested acres if
  // the source is JD data. I want to label whether it's scale tickets, bin measurements, JD
  // data, etc."* Both are facts this panel already held and never put on one line — the page
  // printed the production figure four times and named its source once, eight screens away
  // from the acres it was measured over. `src/claims/productionSources.js` is the rule; it
  // reads every code off `productionRecord.EVIDENCE` rather than typing one.
  const srcs = buildProductionSources(ev, rep.basis);
  const acres = acreBasis(st.fc);
  // ⚠ TWO TABLES MEASURE THIS FARM'S ACRES AND THE CARD WAS QUOTING THE SMALLER ONE AS THE
  // POLICY. Kevin, 2026-10-07: *"I dont understand why there are still so many errors."*
  // This is one, and it is his own earlier request coming back inverted — he asked for 100%
  // when every field is appraised, *"or else its misleading"*, and MULLIGAN now reads
  // **1,536.6 of 1,536.6 insured ac accounted for — 100%** while the two sections below it
  // add to **1,561.0** (LIVINGSTON 1,183.2 + MONROE 377.8).
  //
  // `fieldCoverage` sums `policy_fields.acres` — one row per CLU, the FIELD MAP.
  // The county panel and the guarantee table sum `crop_coverage.acres` — the POLICY.
  // Different tables, different questions, and where a CLU is missing or unmatched the map
  // is short. Declaring 100% of the map is a claim about the policy the policy does not
  // support: 24.4 insured acres were outside the question being answered.
  //
  // One rule, both surfaces — the acres card and the accounted ring — so they cannot drift
  // the way the dollar guarantee did.
  // ⚠ AND THE BASIS IS PLANTED, NOT INSURED. Kevin, 2026-10-07: *"The 24.4 was prevent plant
  // wasn't it? That should not be included. Should only care about planted acres."* The
  // first half is not what the arithmetic says — MULLIGAN's prevent plant is 27.7 ac, it is
  // already its own slice, and it already sits INSIDE the 1,536.6 field-map total — but the
  // rule is right and it is HIS OWN, given on 2026-10-07 for the county table: *"instead of
  // insured acres, it should be planted acres. We dont care about Prevent Plant when it
  // comes to production reporting, just display planted acres."* It was applied to that
  // table and not to this ring or the acres card beside it, which is the same one-surface-
  // at-a-time failure that produced every other defect on this card.
  //
  // On planted acres the whole thing simplifies and the gap SURVIVES, which is how we know
  // the gap is not the prevent plant:
  //     policy   1,561.0 insured - 27.7 PP = 1,533.3 planted
  //     map      1,536.6 insured - 27.7 PP = 1,508.9 planted
  //     gap                                     24.4 planted ac with no field row
  // The prevent-plant slice disappears with the basis, and so does the workaround that had
  // PP counting as "accounted" to stop a fully-walked farm reading short.
  //
  // ⚠ ONE RULE NOW — acreAccounting() in src/claims/fieldCoverage.js. It used to be this
  // inline block plus a second copy of "appraised on file minus matched" in the warning
  // below, and the slice it added had no ceiling: RED DAWN FARMING LLC read 6,985.4 of
  // 5,746.9 planted ac accounted and "-1,238.5 ac still need a production figure". See the
  // rule's own note; tools/check-field-coverage.js holds the identity.
  // ⚠ ONE ANSWER FOR THE WHOLE PAGE. The headline, the by-county table, the to-do list and the
  // scaled-yield gate in ClaimCheckView all read this one rule (see src/claims/claimAccounting.js).
  const acreDenom = panelAcreDenom(st.ev, st.fc);
  const view = claimAccountingView({ fcRows: st.fc ? st.fc.rows : [], counties: ev.counties || [], farm: acreDenom, farmLevel: st.fc ? st.fc.farmLevelAppraisals : [], unplaced: st.fc ? st.fc.unplacedAppraisals : [] });
  const todos = claimTodos({ view, fc: st.fc, sources: srcs });
  const complete = view.total.complete;
  const ac1 = (v) => (v == null ? "—" : v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
  // The claim read is computed in ClaimCheckView, which owns the guarantee and the harvest
  // price. It is PASSED rather than recomputed: two copies of that arithmetic is how the
  // headline comes to disagree with the card below it.
  const cr = claimRead || null;
  // ⚠ ONE PRODUCTION FIGURE FOR THE CARD: the claim engine's (computeHarvestClaim, passed down as
  // claimRead.production). The headline, the % of guarantee and the verdict all read it; the rows
  // above it say what is in it. RAUBERDALE printed 21,300 here, 20,329 as the % basis and 971 in
  // the verdict. Before ClaimCheckView has the panel's figures, this page's own total stands in.
  const prodFig = cr && cr.production != null ? cr.production : view.total.toCountBu;
  const pctGuar = cr && cr.guaranteeBu > 0 && prodFig > 0
    ? Math.round((prodFig / cr.guaranteeBu) * 100) : null;

  return (
    <>
    <JdEvidenceNote report={st.jdEv} opsReport={st.jdOpsEv} season={cropYear} />
    {/* ── WHERE THIS CLAIM STANDS ────────────────────────────────────────────
        The one thing a reader opens this page to find out, at the top instead of eight
        screens down. Guarantee, production, where the production came from, the read, and
        the acres it was measured over. Every figure here appears exactly once on the page. */}
    <div style={{ ...card, marginTop: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <span style={{ fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5a6a5a", fontWeight: 700 }}>Where this claim stands</span>
        {cr && cr.planLabel && <span style={{ fontSize: 10.5, color: "#60735f" }}>{cr.planLabel}</span>}
      </div>

      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 11 }}>
        <span style={{ fontSize: 13, color: "#5a6a5a" }}>Guarantee</span>
        <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 15, fontWeight: 800 }}>{cr ? qty(cr.guaranteeBu, ev.unit) : "—"}</span>
      </div>

      {/* ⚠ PRODUCTION TO COUNT = combine figure + appraisal on CHOPPED acres + appraisal on STANDING
          acres, the SAME three figures the by-county table prints (claimAccountingView). On a
          grain-insured unit the appraisal of chopped acres IS the production (harvestForm.js).
          Appraisal that no field accepted is stated apart below — not added, not hidden. */}
      {(() => {
        const T = view.total;
        const row = (label, val, strong, note) => (
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: strong ? 9 : 5 }}>
            <span style={{ fontSize: strong ? 13 : 12, color: strong ? "#1a2e1a" : "#5a6a5a", fontWeight: strong ? 700 : 400 }}>
              {label}{note && <span style={{ fontSize: 10.5, color: "#60735f", marginLeft: 6 }}>{note}</span>}
            </span>
            <span style={{ fontVariantNumeric: "tabular-nums", fontSize: strong ? 15 : 12.5, fontWeight: strong ? 800 : 600 }}>{qty(val, ev.unit)}</span>
          </div>
        );
        return (
          <>
            <div style={{ borderTop: "1px solid #eef3ee", marginTop: 9 }} />
            {cr && cr.measuredBu > 0 && row("Scale tickets / records on file", cr.measuredBu)}
            {T.bu.combined > 0 && row("Combined (combine figure)", T.bu.combined, false,
              cr && cr.combineCounted === false && cr.measuredBu > 0 ? "not added: records on file come first" : null)}
            {T.bu.chopped > 0 && row("Appraisal on chopped acres", T.bu.chopped, false, cr && cr.appraisalsCounted === false ? "not added (switched off)" : "counts as production")}
            {T.bu.appraised > 0 && row("Appraisal on standing acres", T.bu.appraised, false, cr && cr.appraisalsCounted === false ? "not added (switched off)" : "its own record type")}
            {T.bu.appraisedUnplaced > 0 && row("Appraised, field not identified", T.bu.appraisedUnplaced, false, cr && cr.appraisalsCounted === false ? "not added (switched off)" : "counted at its unit")}
            {row(complete ? "Production to count" : "Production to count so far", prodFig, true)}
            {/* ⚠ INSURED-AS-SILAGE ACRES ARE NOT IN THE FIGURE ABOVE AND NEVER WILL BE — a ton is
                not a bushel (7 CFR 457.113 §12(b); claimAccounting.js silageOf). The JD chopper
                tonnage is a machine estimate, never production to count (Corn LASH ¶14). */}
            {T.silage && (T.silage.estAc > 0.05 || T.silage.noneAc > 0.05) && (
              <div style={{ fontSize: 11.5, color: "#1f5f6a", marginTop: 5, lineHeight: 1.45 }}>
                Insured as silage, not added above: {T.silage.wetTons > 0
                  ? <><b>{T.silage.wetTons.toLocaleString()} wet t</b> JD chopper estimate on {ac1(T.silage.estAc)} ac</>
                  : <>no silage figure yet</>}
                {T.silage.noneAc > 0.05 && T.silage.wetTons > 0 && <> · {ac1(T.silage.noneAc)} ac with no silage figure</>}.
              </div>
            )}
            {T.bu.unmatched > 0 && (
              <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 5, lineHeight: 1.45 }}>
                Another <b>{qty(T.bu.unmatched, ev.unit)}</b>{T.unmatchedAc > 0.05 && <> (~{ac1(T.unmatchedAc)} ac)</>} of
                appraisal is on file but not tied to a field, so it is not in the figure above.
              </div>
            )}
            {!cr && rep.quantity != null && rep.quantity !== T.bu.combined && row("Scale tickets / records on file", rep.quantity, false, "not added above")}
          </>
        );
      })()}
      {srcs.label && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 5, flexWrap: "wrap" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: srcs.monitorChosen ? "#c49a2a" : "#2d6a2d", flexShrink: 0 }} />
          <span style={{ fontSize: 11.5, color: "#1a2e1a", fontWeight: 700 }}>{srcs.label}</span>
          {srcs.code && <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 10.5, fontWeight: 700, border: "1px solid #dde8d8", borderRadius: 4, padding: "2px 5px", color: "#5a6a5a", background: "#f7faf7" }}>{srcs.code}</span>}
        </div>
      )}

      {/* ── THE ONE ACREAGE ANSWER, IN A SENTENCE ── from the same rule as the table below. */}
      <div style={{ marginTop: 11, padding: "8px 11px", borderRadius: 7, fontSize: 13, fontWeight: 700, lineHeight: 1.45,
                    background: complete ? "#e8f6ec" : "#fdf7ea", border: "1px solid " + (complete ? "#bfe3c8" : "#ecdca6"),
                    color: complete ? "#2d6a2d" : "#7a5c00" }}>
        {/* `complete` is the GRAIN read (claimAccounting); silage-insured acres waiting on tons are
            stated beside it as the silage line's own status, never as a hole in the grain read. */}
        {complete
          ? (view.total.everyAcre
            ? <>Every one of the {ac1(view.total.planted)} planted ac has a production figure.</>
            : <>Every grain acre has a production figure. {ac1(view.total.silage.nothing)} ac insured as silage are waiting on tons (silage line, not the grain read).</>)
          : <>{ac1(view.total.grainNothing)} of {ac1(view.total.grainPlanted)} {view.total.grainPlanted < view.total.planted - 0.05 ? "grain-insured " : ""}planted ac have no production figure yet{view.total.silage.nothing > 0.05 ? <>; {ac1(view.total.silage.nothing)} ac insured as silage are waiting on tons</> : null}.</>}
      </div>

      {/* ⚠ NO OVER/UNDER AND NO PER-ACRE YIELD WHILE ANY PLANTED ACRE IS UNACCOUNTED. Production
          that covers part of the farm, read against the whole farm's guarantee, values every
          other acre at ZERO bushels and inverts the sign of the claim (Rogers Farms; Red Dawn). */}
      {!complete ? (
        <div style={{ borderTop: "1px solid #eef3ee", marginTop: 11, paddingTop: 10 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#8a6d1f" }}>Claim can&rsquo;t be read yet</div>
          <div style={{ fontSize: 11.5, color: "#6a7a6a", marginTop: 3, lineHeight: 1.45 }}>
            Reading a shortfall now would count the <strong>{ac1(view.total.grainNothing)} ac</strong> with no
            figure as <strong>zero bushels</strong> and overstate the claim. Work down the to-do list
            below; the read appears once every planted acre has a figure.
          </div>
        </div>
      ) : cr && cr.hasClaim != null ? (
        <div style={{ borderTop: "1px solid #eef3ee", marginTop: 11, paddingTop: 10, display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: cr.hasClaim ? "#b3300f" : "#2d6a2d" }}>{cr.hasClaim ? "Likely claim — open it" : "No claim indicated"}</span>
          {pctGuar != null && <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 15, fontWeight: 800, color: cr.hasClaim ? "#b3300f" : "#2d6a2d" }}>{pctGuar}%</span>}
        </div>
      ) : (
        <div style={{ borderTop: "1px solid #eef3ee", marginTop: 11, paddingTop: 10 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#8a6d1f" }}>No claim read yet</div>
          <div style={{ fontSize: 11.5, color: "#6a7a6a", marginTop: 3, lineHeight: 1.45 }}>
            A claim is read against production that has been <strong>counted</strong> — enter or import it
            by county below{pctGuar != null ? `; on the evidence alone this would be ${pctGuar}% of guarantee` : ""}.
          </div>
        </div>
      )}

      {/* ── ACRES AND BUSHELS BY HOW EACH ACRE IS ACCOUNTED FOR: ONE CARD PER COUNTY ──
          Every figure is acreAccounting() (src/claims/fieldCoverage.js), regrouped by
          src/claims/claimAccounting.js, so the rows add to the planted acres. Yield is production over the
          ACCOUNTED acres only — never over insured acres while any acre has no figure. */}
      <div style={{ borderTop: "1px solid #eef3ee", marginTop: 11, paddingTop: 11 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5a6a5a", fontWeight: 700, marginBottom: 6 }}>
          By county — acres, bushels and yield
        </div>
        {(() => {
          const cards = view.lines.length > 1 ? [...view.lines, view.total] : view.lines;
          const r1c = (v) => +(+v).toFixed(1);
          const acCell = (v) => (v > 0.05 ? ac1(v) : "—");
          const buCell = (v) => (v > 0 ? Math.round(v).toLocaleString() : "—");
          const ypa = (bu, ac) => (bu > 0 && ac > 0.05 ? (bu / ac).toFixed(1) : "—");
          return (
            <div style={{ display: "grid", gap: 10 }}>
              {cards.map((l) => {
                const isTot = l === view.total;
                const accounted = r1c(l.planted - l.nothing);
                const hmcAc = l.hmcAc || 0, hmcBu = (l.bu && l.bu.hmc) || 0;
                const chopAc = Math.max(0, r1c(l.chopped - hmcAc)), chopBu = Math.max(0, l.bu.chopped - hmcBu);
                const ppAc = l.acc ? l.acc.policyPp : (l.ppAcres || 0);
                // Silage-insured acres (026) are their own rows below the grain total, in tons.
                const sil = l.silage || { estAc: 0, noneAc: 0, nothing: 0, wetTons: 0, dmTons: 0, machineAc: 0, buAppr: { fields: 0, acres: 0, bu: 0 } };
                const silPlanted = r1c(sil.estAc + sil.nothing);
                const grainNothing = l.grainNothing != null ? l.grainNothing : l.nothing;
                const rows = [
                  ["Insured (planted)", l.planted, null, null, ppAc > 0.05 ? "excl. " + ac1(ppAc) + " PP" : null],
                  ["Chopped, valued at the appraisal", chopAc, chopBu, true],
                  ["Combined (incl. appraised then combined)", l.combined, l.bu.combined, true],
                  ...(hmcAc > 0.05 || hmcBu > 0 ? [["High-moisture corn, valued at the appraisal", hmcAc, hmcBu, true]] : []),
                  ["Appraised, still standing", l.appraised, l.bu.appraised, true],
                  // Counted at its unit (Kevin: production counts at the unit), never guessed onto a field.
                  ...((l.appraisedUnplaced || 0) > 0.05 || (l.bu.appraisedUnplaced || 0) > 0
                    ? [["Appraised, field not identified", l.appraisedUnplaced || 0, l.bu.appraisedUnplaced || 0, true, "counted at its unit"]] : []),
                  ["No production figure yet", grainNothing, null, null],
                ];
                // Grain production so far is over the GRAIN acres that have a figure.
                const grainAccounted = r1c(accounted - sil.estAc);
                const cell = { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
                return (
                  <div key={l.county} style={{ border: "1px solid #e3ece3", borderRadius: 8, padding: "9px 11px", background: isTot ? "#f2f6f2" : "#fcfdfc", minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 13.5, color: "#1a2e1a", marginBottom: 6 }}>{l.county}
                      {silPlanted > 0.05 && <span style={{ fontWeight: 400, fontSize: 10.5, color: "#60735f", marginLeft: 6 }}>incl. {ac1(silPlanted)} ac insured as silage</span>}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto auto", gap: "4px 12px", fontSize: 12, alignItems: "baseline" }}>
                      <div />
                      <div style={{ ...cell, fontSize: 10, color: "#60735f", fontWeight: 700 }}>Acres</div>
                      <div style={{ ...cell, fontSize: 10, color: "#60735f", fontWeight: 700 }}>Bushels</div>
                      <div style={{ ...cell, fontSize: 10, color: "#60735f", fontWeight: 700 }}>bu/ac</div>
                      {rows.map(([label, ac, bu, withBu, note]) => (
                        <React.Fragment key={label}>
                          <div style={{ color: "#5a6a5a", minWidth: 0 }}>{label}{note && <span style={{ color: "#60735f", fontSize: 10.5, marginLeft: 6 }}>{note}</span>}</div>
                          <div style={{ ...cell, fontWeight: ac > 0.05 ? 700 : 400, color: ac > 0.05 ? (label.startsWith("No production") ? "#a33" : "#1a2e1a") : "#aab5aa" }}>{acCell(ac)}</div>
                          <div style={{ ...cell, color: bu > 0 ? "#1a2e1a" : "#aab5aa" }}>{withBu ? buCell(bu) : ""}</div>
                          <div style={{ ...cell, color: "#6a7a6a" }}>{withBu ? ypa(bu, ac) : ""}</div>
                        </React.Fragment>
                      ))}
                      <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e3ece3", margin: "2px 0" }} />
                      <div style={{ fontWeight: 800, color: "#1a2e1a", minWidth: 0 }}>Total production so far{silPlanted > 0.05 ? " (grain)" : ""}
                        <span style={{ color: "#60735f", fontSize: 10.5, fontWeight: 400, marginLeft: 6 }}>on {ac1(grainAccounted)} accounted ac</span></div>
                      <div style={{ ...cell, fontWeight: 800 }}>{acCell(grainAccounted)}</div>
                      <div style={{ ...cell, fontWeight: 800 }}>{buCell(l.toCountBu != null ? l.toCountBu : l.bu.combined + l.bu.chopped + l.bu.appraised)}</div>
                      <div style={{ ...cell, fontWeight: 800 }}>{ypa(l.toCountBu != null ? l.toCountBu : l.bu.combined + l.bu.chopped + l.bu.appraised, grainAccounted)}</div>
                      {/* ── INSURED AS SILAGE (026): its own section, in TONS, never added to the
                          bushels above (Kevin: "we never merge those"; 7 CFR 457.113 §12(b)). The
                          JD chopper figure is a machine estimate in wet t at 35% DM (a unit
                          conversion of John Deere's dry matter, harvestForm.js), never production
                          to count (Corn LASH ¶14). A bushel appraisal here is the wrong unit
                          (Kevin, 2026-11-03): shown as written, flagged, not counted. */}
                      {silPlanted > 0.05 && (
                        <>
                          <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e3ece3", margin: "4px 0 0" }} />
                          <div style={{ gridColumn: "1 / -1", fontSize: 10.5, fontWeight: 700, color: "#1f5f6a" }}>Insured as silage · tons, not added to bushels</div>
                          <div style={{ color: "#5a6a5a", minWidth: 0 }}>JD chopper estimate<span style={{ color: "#60735f", fontSize: 10.5, marginLeft: 6 }}>wet t at 35% DM</span></div>
                          <div style={{ ...cell, fontWeight: sil.estAc > 0.05 ? 700 : 400, color: sil.estAc > 0.05 ? "#1a2e1a" : "#aab5aa" }}>{acCell(sil.estAc)}</div>
                          <div style={{ ...cell, color: sil.wetTons > 0 ? "#1a2e1a" : "#aab5aa" }}>{sil.wetTons > 0 ? sil.wetTons.toLocaleString() + " t" : "—"}</div>
                          <div style={{ ...cell, color: "#6a7a6a" }}>{sil.wetTons > 0 && sil.machineAc > 0.05 ? (sil.wetTons / sil.machineAc).toFixed(1) + " t/ac" : ""}</div>
                          <div style={{ color: "#5a6a5a", minWidth: 0 }}>No silage figure yet</div>
                          <div style={{ ...cell, fontWeight: sil.nothing > 0.05 ? 700 : 400, color: sil.nothing > 0.05 ? "#a33" : "#aab5aa" }}>{acCell(sil.nothing)}</div>
                          <div /><div />
                          {sil.buAppr.fields > 0 && (
                            <div style={{ gridColumn: "1 / -1", fontSize: 10.5, color: "#8a3324", lineHeight: 1.4 }}>
                              ⚠ Appraised in bushels ({ac1(sil.buAppr.acres)} ac · {sil.buAppr.bu.toLocaleString()} bu as written) — wrong unit for silage-insured; corrected appraisal needed. Not counted.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 10.5, color: "#60735f", lineHeight: 1.45 }}>
                Combined = combine harvest (used over an appraisal). Chopped = chopped on a grain policy, valued at its appraisal.
                Insured as silage = tons, never added to bushels. Yield is over accounted acres only.
                {view.mode === "farm" && (ev.counties || []).length > 1 && " The county rows did not add back to the planted acres, so this is shown for the whole farm."}
              </div>
            </div>
          );
        })()}
      </div>

      {st.uy && <UnitYieldBlock uy={st.uy} open={openUnits} setOpen={setOpenUnits} ac1={ac1} hasTickets={!!st.hasTickets} />}
      {!st.uy && st.uyErr && (
        <div style={{ borderTop: "1px solid #eef3ee", marginTop: 11, paddingTop: 11, fontSize: 11.5, color: "#8a3324" }}>
          Yield per acre by unit couldn't load ({st.uyErr}). The county figures above are unaffected.
        </div>
      )}

      {/* ── WHAT TO DO NEXT ── derived from the counts above, never invented. */}
      <div style={{ borderTop: "1px solid #eef3ee", marginTop: 11, paddingTop: 11 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5a6a5a", fontWeight: 700, marginBottom: 6 }}>To do</div>
        {todos.length ? (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#4a4a2a", lineHeight: 1.5 }}>
            {todos.map((t) => <li key={t.key} style={{ marginTop: 3 }}>{t.text}</li>)}
          </ul>
        ) : (
          <div style={{ fontSize: 12.5, color: "#2d6a2d", fontWeight: 700 }}>Nothing outstanding on the acreage.</div>
        )}
      </div>
    </div>

      {/* ── DETAILS ── everything that used to sit above the answer. Nothing was deleted: the field
          roll call, the record-type explanations, the matching notes, the sources and the
          production-report form are all still here, one tap down. */}
      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: "pointer", fontSize: 13.5, fontWeight: 800, color: "#2d4a2d", padding: "6px 0" }}>
          Details — fields, sources, matching notes and the production report
        </summary>
        {acres.harvestedAcres != null && (
          <div style={{ fontSize: 11.5, color: "#5a6a5a", margin: "4px 0 2px", lineHeight: 1.45 }}>
            John Deere harvested <b>{ac1(acres.harvestedAcres)}</b> ac of the mapped insured ground.
            {acres.uncoveredAcres != null && acres.uncoveredAcres > 0.05 && <> {ac1(acres.uncoveredAcres)} ac of it have no combine pass.</>}
            {acres.unmappedAcres > 0 && <> {ac1(acres.unmappedAcres)} ac of combine pass could not be placed on a field.</>}
          </div>
        )}
      {/* ── WHICH INSURED FIELDS ARE ACCOUNTED FOR ──────────────────────────
          Kevin, 2026-10-01: appraised / harvested / both / neither per insured field.
          The totals above say how many BUSHELS; this says whether every FIELD has been
          spoken for, which a total can never show — a complete-looking number over an
          incomplete set of fields is the failure this section exists to surface. */}
      {st.fc && st.fc.totals.fields ? (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #eef3ec" }}>
          <div style={h}>Every insured field, and what accounts for it</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            {[
              ["Appraised", st.fc.totals.appraisedOnly, "#8a6d1f", "#fdf8e8"],
              ["Harvested", st.fc.totals.harvestedOnly, "#2d6a2d", "#eef7ec"],
              ["Appraised + combined — combine used", st.fc.totals.both, "#1a4a7a", "#eaf2fa"],
              // ⚠ APPRAISED AND CHOPPED IS NOT APPRAISED AND COMBINED (Kevin, 2026-10-26): on a
              // grain policy the chopper's tonnage is not grain, so the APPRAISAL is the figure.
              ...(st.fc.totals.bothChopped
                ? [["Appraised + chopped — appraisal used", st.fc.totals.bothChopped, "#5a3a98", "#f1ecfa"]] : []),
              ...(st.fc.totals.bothUnknown
                ? [["Appraised + harvest type unknown — appraisal used", st.fc.totals.bothUnknown, "#5a3a98", "#f1ecfa"]] : []),
              ...(st.fc.totals.chopped
                ? [["Chopped, not appraised — no grain figure", st.fc.totals.chopped, "#a33", "#fdecec"]] : []),
              ...(st.fc.totals.harvestUnknown
                ? [["Harvested, type unknown — no grain figure", st.fc.totals.harvestUnknown, "#a33", "#fdecec"]] : []),
              // Insured as silage (026): tons, never grain — see coverageState.
              ...(st.fc.totals.silageChopped
                ? [["Insured as silage, chopped — JD chopper estimate", st.fc.totals.silageChopped, "#1f5f6a", "#e8f4f5"]] : []),
              ...(st.fc.totals.silageNone
                ? [["Insured as silage — no silage figure yet", st.fc.totals.silageNone, "#a33", "#fdecec"]] : []),
              // ⚠ A PASS WITH NO MAP IS ITS OWN STATE, not a quieter "nothing yet". The
              // combine WAS there and John Deere states what came off; what is missing is the
              // grid that would split it across fields. Folding it into "nothing yet" is what
              // sent Kevin looking for a ripped-up wheat field that did not exist.
              ...(st.fc.totals.unmapped
                ? [["Pass on file — map not built", st.fc.totals.unmapped, "#8a5a1f", "#fdf1e4"]]
                : []),
              // ⚠ SHOWN ONLY WHERE THERE IS ANY, like the pass-with-no-map chip beside it.
              // A "Prevent plant 0" on every other farm is noise, and a reader learns to
              // skip a row that is almost always zero.
              ...(st.fc.totals.preventPlantFields
                ? [["Prevent plant — settled", st.fc.totals.preventPlantFields, "#5a6a7a", "#eef2f6"]]
                : []),
              ["Nothing yet", st.fc.totals.neither, st.fc.totals.neither ? "#a33" : "#60735f",
               st.fc.totals.neither ? "#fdecec" : "#f4f7f3"],
            ].map(([label, n, fg, bg]) => (
              <div key={label} style={{ background: bg, color: fg, border: `1px solid ${fg}22`,
                borderRadius: 8, padding: "6px 10px", fontSize: 12 }}>
                <b style={{ fontSize: 15 }}>{n}</b> {label}
              </div>
            ))}
          </div>
          {/* ⚠ NOTHING-YET IS ONLY A FINDING ONCE THE CROP IS OFF. Mid-season it means the
              combine has not been there, which is not a hole in anything. Saying so is the
              difference between a useful list and 85 false alarms on 1 October. */}
          {st.fc.totals.neither ? (
            <div style={{ fontSize: 12.5, color: "#8a6d1f", marginBottom: 8 }}>
              <b>{st.fc.totals.neither} field{st.fc.totals.neither === 1 ? "" : "s"} ({st.fc.totals.neitherAcres.toLocaleString()} ac)</b>{" "}
              have neither an appraisal nor a combine pass. Before harvest that means the
              combine has not been there yet; once the crop is off it is production nobody
              has stated.
            </div>
          ) : null}
          {/* ⚠ THE COMBINE WAS THERE. This reads as a finding about the DATA, not about the
              farm — the distinction the "nothing yet" line above cannot make. */}
          {st.fc.totals.unmapped ? (
            <div style={{ fontSize: 12.5, color: "#8a5a1f", marginBottom: 8, background: "#fdf1e4",
              border: "1px solid #e8d4b8", borderRadius: 8, padding: "8px 10px" }}>
              <b>{st.fc.totals.unmapped} field{st.fc.totals.unmapped === 1 ? "" : "s"} ({st.fc.totals.unmappedAcres.toLocaleString()} ac)</b>{" "}
              sit under a combine pass whose yield map has not been built. The pass is on file and
              John Deere states what came off it; what is missing is the grid that would split it
              across your fields, so no per-field figure is shown for them.
              {st.fc.unmappedPasses.map((p) => (
                <div key={p.opId} style={{ fontSize: 11.5, color: "#6a4a1a", marginTop: 4 }}>
                  <b>{p.fieldName || p.fieldId || "a John Deere field"}</b>
                  {p.date ? " · " + String(p.date).slice(0, 10) : ""} · {p.acres.toLocaleString()} ac ·{" "}
                  {p.perAcre.toLocaleString()} {p.unit || "per ac"} stated ={" "}
                  <b>{p.quantity.toLocaleString()}</b>
                  {p.fieldCount > 1 ? " — one pass covering " + p.fieldCount + " insured fields, which is why it is not split" : ""}
                </div>
              ))}
              {/* The route that actually fixes it, rather than a dead end. */}
              <div style={{ fontSize: 11.5, color: "#6a4a1a", marginTop: 6 }}>
                Export that operation from the John Deere Operations Center and upload it on
                Field Mapper → Machine Data, and the map builds from the file.
              </div>
            </div>
          ) : null}
          {st.fc.totals.both ? (
            <div style={{ fontSize: 12.5, color: "#1a4a7a", marginBottom: 8 }}>
              <b>{st.fc.totals.both} appraised field{st.fc.totals.both === 1 ? " was" : "s were"} then harvested.</b>{" "}
              The yield figure is the one to report — an appraisal estimates what standing crop
              would make, and the combine measured what came off. Counting both would double
              the field.
            </div>
          ) : null}
          {st.fc.totals.appraisedAtFarmLevel ? (
            <div style={{ fontSize: 12.5, color: "#5a6a5a", marginBottom: 8 }}>
              {st.fc.totals.appraisedAtFarmLevel} field{st.fc.totals.appraisedAtFarmLevel === 1 ? " is" : "s are"}{" "}
              accounted for at the <b>farm number</b> rather than individually: one worksheet
              line covers several fields, and the farm's appraised acres reconcile to its
              insured acres. No per-field bushels are shown for those, because the worksheet
              states none.
            </div>
          ) : null}
          {/* ⚠ THE PER-FIELD ROLL-CALL IS FOLDED, NOT DELETED.
            *
            * Kevin, 2026-10-06, on a Mulligan appraisal upload: *"this is now a massive
            * amount of data that seems to be massively repetitive. I want to see insured
            * acres by county/unit. Appraised acres by county/unit, and then appraised
            * bushels by field, and appraised acres summed. Seems like this should be much
            * cleaner and simpler."*
            *
            * He is right that it repeats: the appraisal table above already lists all 61
            * fields with their acres and bushels, and this table lists the same fields
            * again with different columns. Two 60-row tables over one set of fields is the
            * repetition, and on a phone it is most of the page.
            *
            * ⚠ BUT THIS TABLE ANSWERS A QUESTION THE OTHER ONE CANNOT: whether every
            * INSURED field is spoken for. The appraisal table only lists fields the
            * worksheet named — a field nobody appraised is, by construction, absent from
            * it. That is the whole point of this block and it is why the summary above
            * (the chips, the farm-level note, the untied lines) stays on screen in full.
            * Only the row-by-row repeat is behind the click.
            */}
          <details style={{ marginTop: 2 }}>
            <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#2d4a2d", padding: "4px 0" }}>
              Field-by-field roll call ({st.fc.totals.fields} insured field{st.fc.totals.fields === 1 ? "" : "s"})
            </summary>
          {hmcErr && <div style={{ fontSize: 11.5, color: "#8a3324", fontWeight: 600, margin: "4px 0" }}>⚠ {hmcErr}</div>}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#5a6a5a", borderBottom: "1px solid #e3ece1" }}>
                  <th style={{ padding: "4px 6px" }}>County</th>
                  <th style={{ padding: "4px 6px" }}>Farm / tract / field</th>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>Insured ac</th>
                  {/* Kevin, 2026-10-01: "show side by side insured acres and JD monitor acres
                      so we can see if they are a match". The monitor's acres are what the
                      combine actually covered; a gap either way is a reporting question. */}
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>Monitor ac</th>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>Δ</th>
                  <th style={{ padding: "4px 6px" }}>Accounted for by</th>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>Appraised</th>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>Harvested</th>
                  <th style={{ padding: "4px 6px" }}>High-moisture corn</th>
                </tr>
              </thead>
              <tbody>
                {/* ⚠ THE UNACCOUNTED-FOR TAIL IS COLLAPSED, AND ONLY FOUND BY RENDERING IT.
                    Rogers is 108 rows of which 85 read "nothing yet", so in a flat table the
                    23 rows that carry evidence are a needle in a wall of dashes. The COUNT and
                    the ACRES are in the banner above, which is where the finding belongs —
                    this only hides the repetition, and never the number. */}
                {(showAllFields ? st.fc.rows : st.fc.rows.filter((r) => r.state !== "neither")).map((r) => {
                  const tone = (r.state === "neither" || r.state === "chopped" || r.state === "harvest_unknown" || r.state === "silage_none") ? "#a33"
                    : r.state === "silage_chopped" ? "#1f5f6a"
                    : r.state === "both" ? "#1a4a7a" : (r.state === "both_chopped" || r.state === "both_unknown") ? "#5a3a98"
                    : r.state === "harvested" ? "#2d6a2d" : r.state === "unmapped" ? "#8a5a1f" : "#8a6d1f";
                  const what = r.state === "neither" ? "nothing yet"
                    : r.state === "both" ? "appraised + combined — combine used"
                    : r.state === "both_chopped" ? "appraised + chopped — appraisal used"
                    : r.state === "both_unknown" ? "appraised + harvest type unknown — appraisal used"
                    : r.state === "chopped" ? "chopped, not appraised — no grain figure"
                    : r.state === "harvest_unknown" ? "harvested, type unknown — no grain figure"
                    : r.state === "silage_chopped" ? "insured as silage, chopped — JD chopper estimate"
                    : r.state === "silage_none" ? "insured as silage — no silage figure yet"
                    : r.state === "harvested" ? "combine"
                    : r.state === "unmapped" ? "combine pass — map not built"
                    : r.appraised && r.appraised.atFarmLevel ? "appraisal (at the farm number)" : "appraisal";
                  // A grain field the combine and the chopper both crossed: its second part, by acres.
                  const partWhat = r.splitPart ? ` · ${r.splitPart.acres.toFixed(1)} ac ${r.splitPart.state === "harvested" ? "combined (combine used)"
                    : r.splitPart.state === "both_chopped" ? "chopped (appraisal used)" : "chopped, not appraised"}` : "";
                  // ⚠ ONLY A GRIDDED PASS STATES ACRES FOR THIS FIELD. An unmapped pass's acres
                  // belong to the whole John Deere field, so putting them in this column would
                  // compare one field's insured acres against several fields' monitor acres.
                  const monAc = r.harvested ? r.harvested.acres : null;
                  const dAc = monAc != null && r.insuredAcres ? monAc - r.insuredAcres : null;
                  // A gap worth a second look. Tight enough that an ordinary headland rounding
                  // does not light up, loose enough to catch a field reported at the wrong size.
                  const dBad = dAc != null && Math.abs(dAc) > Math.max(1, r.insuredAcres * 0.05);
                  return (
                    <tr key={r.cluId} style={{ borderBottom: "1px solid #f2f6f1" }}>
                      <td style={{ padding: "4px 6px" }}>{r.county || "—"}</td>
                      {/* nowrap: on a phone this wrapped to three lines and made every row three
                          rows tall, which is most of why the table read as a wall. */}
                      <td style={{ padding: "4px 6px", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {r.fsn || "—"} / {r.tract || "—"} / {r.fieldId || "—"}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {r.insuredAcres ? r.insuredAcres.toFixed(1) : "—"}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                        color: monAc == null ? "#aab5aa" : "#2d4a2d" }}>
                        {monAc == null ? "—" : monAc.toFixed(1)}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                        color: dAc == null ? "#aab5aa" : dBad ? "#a33" : "#6a7a6a",
                        fontWeight: dBad ? 700 : 400 }}>
                        {dAc == null ? "—" : (dAc > 0 ? "+" : "") + dAc.toFixed(1)}
                      </td>
                      <td style={{ padding: "4px 6px", color: tone, fontWeight: (r.state === "neither" || r.state === "unmapped" || r.state === "chopped" || r.state === "harvest_unknown" || r.state === "silage_none") ? 700 : 600 }}>
                        {r.splitPart ? `${what} on ${(r.stateAcres || 0).toFixed(1)} ac${partWhat}` : what}
                        {r.mixedNote && <div style={{ fontSize: 10.5, color: "#8a6d1f", fontWeight: 500 }}>combined and chopped: {r.mixedNote}</div>}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                        textDecoration: r.supersededAppraisal || r.wrongUnitAppraisal ? "line-through" : "none",
                        color: r.supersededAppraisal || r.wrongUnitAppraisal ? "#60735f" : "inherit" }}
                        title={r.wrongUnitAppraisal ? "Appraised in bushels — wrong unit for silage-insured; corrected appraisal needed. Not counted." : undefined}>
                        {/* ⚠ A FARM-LEVEL APPRAISAL PRINTS "at farm level", NEVER 0 — the
                            worksheet states no per-field figure, and a dash would read as
                            "not appraised" on a field that is. */}
                        {!r.appraised ? "—"
                          : r.appraised.acres == null ? <span style={{ color: "#60735f" }}>at farm level</span>
                          : `${r.appraised.acres.toFixed(1)} ac${r.appraised.quantity ? " · " + r.appraised.quantity.toLocaleString() : ""}`}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {/* ⚠ AN UNMAPPED PASS PRINTS THE JOHN DEERE FIELD'S OWN FIGURE AND SAYS
                            SO. A dash would repeat the "the combine was never here" error one
                            cell further in; a bare number would read as this field's measured
                            yield when it is the whole pass's stated one. */}
                        {/* ⚠ A CHOPPED (or undeterminable) PASS PRINTS NO QUANTITY. Its figure is
                            wet silage tonnage; beside a bushel appraisal it reads as grain. */}
                        {/* A silage-insured chopped field prints its JD chopper estimate IN TONS. */}
                        {r.silageEstimate
                          ? <span style={{ color: "#1f5f6a" }} title="John Deere chopper estimate: dry matter restated as wet tons at 35% DM. A machine estimate, not production to count.">
                              {r.silageEstimate.acres.toFixed(1)} ac · {r.silageEstimate.wetTons.toLocaleString()} wet t ({r.silageEstimate.wetPerAcre} t/ac)
                            </span>
                          : r.combinePart
                          ? <span title="Combine bushels count on the combined acres; the chopper's figure is not grain production">
                              {r.combinePart.acres.toFixed(1)} ac · {r.combinePart.quantity.toLocaleString()} combined
                              <div style={{ fontSize: 10.5, color: "#8a6a5a" }}>{(r.harvested.chopAcres || 0).toFixed(1)} ac chopped, not counted</div>
                            </span>
                          : r.harvested && r.machineSetAside
                          ? <span style={{ color: "#8a6a5a" }} title="Not grain production — the appraisal is the figure">
                              {r.harvested.acres.toFixed(1)} ac · {r.hmcMarked ? "high-moisture corn" : r.harvestForm === "chopped" ? "chopped" : r.harvestForm === "combined" ? "combined on a silage policy" : "type unknown"}, not counted
                            </span>
                          : r.harvested ? `${r.harvested.acres.toFixed(1)} ac · ${r.harvested.quantity.toLocaleString()}`
                          : r.unmappedPass ? (
                            /* ⚠ THE FIGURE IS NOT REPEATED PER ROW. Rendered against Bridgewater,
                               both of 180-Tim Creek's fields printed "149.1 ac · 17,681" — the same
                               pass twice, side by side, inviting a reader to add them to 298 ac and
                               35,362 bu. The pass is ONE combine run; it is stated once in the
                               banner above and named here, so the row says which pass covers this
                               field without restating what it made. */
                            <span style={{ color: "#8a5a1f", fontWeight: 600 }}>
                              {(r.unmappedPass.list[0] || {}).fieldName || "a John Deere field"}
                              <div style={{ fontSize: 10.5, color: "#a07a4a", fontWeight: 500 }}>map not built — see above</div>
                            </span>
                          ) : "—"}
                      </td>
                      <td style={{ padding: "4px 6px", fontSize: 11, minWidth: 150 }}>
                        {/* ⚠ THE SWITCH IS A HUMAN DECISION. A combine can harvest high-moisture corn (fed
                            on the farm, never sold as grain); on a grain-insured unit that field is valued by
                            its APPRAISAL like a chopped one. John Deere data cannot tell it from wet shelled
                            corn, so the moisture chip only POINTS and never sets the switch. */}
                        {canMarkHmc && (r.harvested || r.hmc) && ((r.typeCode && r.typeCode !== "026") || r.hmc) ? (
                          <label style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer", fontWeight: 700, color: r.hmcMarked ? "#5a3a98" : "#4a5a4a" }}>
                            <input type="checkbox" checked={!!r.hmc} disabled={hmcBusy === r.cluId}
                              onChange={(e) => toggleHmc(r, e.target.checked)} />
                            Harvested as HMC
                          </label>
                        ) : r.hmcMarked ? <b style={{ color: "#5a3a98" }}>HMC</b> : "—"}
                        {r.moistureAbove && !r.hmcMarked && (
                          <div><span style={{ display: "inline-block", marginTop: 2, border: "1px solid #e8d4b8", background: "#fdf1e4", color: "#8a5a1f", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}
                            title="The combine pass shows moisture above 28%. This only flags the field — it does not mark it.">{r.moisturePct.toFixed(1)}% moisture</span></div>
                        )}
                        {r.hmcNotApplied && (
                          <div style={{ color: "#8a5a1f", marginTop: 2, fontWeight: 600 }}>not applied — the insured corn type is not stated, so the combine figure is used</div>
                        )}
                        {r.hmc && (
                          <div style={{ color: "#60735f", marginTop: 2 }}>set by {r.hmc.by || "—"}{r.hmc.at ? " · " + String(r.hmc.at).slice(0, 10) : ""}</div>
                        )}
                        {r.hmc && !r.harvested && <div style={{ color: "#8a6d1f" }}>no combine pass — no effect</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* ⚠ THE TOTALS ARE THE QUESTION THE TABLE EXISTS TO ANSWER, and they were only
                    derivable by adding twelve rows by hand. Insured against monitor, side by side,
                    on the SAME rows the table is showing — a total over hidden rows would not match
                    what is on screen. The monitor total counts only fields with a measured grid,
                    because an unmapped pass states acres for a whole John Deere field and adding
                    those to per-field acres would double-count the ground it covers. */}
              <tfoot>
                {(() => {
                  const shown = showAllFields ? st.fc.rows : st.fc.rows.filter((r) => r.state !== "neither");
                  const ins = shown.reduce((a, r) => a + (r.insuredAcres || 0), 0);
                  const mon = shown.reduce((a, r) => a + (r.harvested ? r.harvested.acres : 0), 0);
                  const withMon = shown.filter((r) => r.harvested).length;
                  const d = mon > 0 ? mon - ins : null;
                  return (
                    <tr style={{ borderTop: "2px solid #cfe0cb", fontWeight: 800, color: "#1a2e1a" }}>
                      <td style={{ padding: "6px 6px" }} colSpan={2}>
                        {shown.length} field{shown.length === 1 ? "" : "s"}
                        {showAllFields ? "" : st.fc.totals.neither ? " shown" : ""}
                      </td>
                      <td style={{ padding: "6px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{ins.toFixed(1)}</td>
                      <td style={{ padding: "6px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {mon > 0 ? mon.toFixed(1) : "—"}
                      </td>
                      <td style={{ padding: "6px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                        color: d == null ? "#aab5aa" : Math.abs(d) > Math.max(1, ins * 0.05) ? "#a33" : "#2d6a2d" }}>
                        {d == null ? "—" : (d > 0 ? "+" : "") + d.toFixed(1)}
                      </td>
                      <td style={{ padding: "6px 6px", fontWeight: 600, color: "#5a6a5a", fontSize: 11 }}>
                        {/* ⚠ THE DENOMINATOR IS NAMED. A monitor total over 10 of 12 fields read beside an
                            insured total over all 12 is two different footprints on one line. */}
                        {withMon < shown.length ? `monitor covers ${withMon} of ${shown.length}` : "every field"}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
          {st.fc.totals.neither ? (
            <button onClick={() => setShowAllFields(!showAllFields)}
              style={{ marginTop: 6, background: "none", border: "1px solid #dde8d8", borderRadius: 6,
                padding: "4px 10px", fontSize: 12, color: "#2d4a2d", cursor: "pointer" }}>
              {showAllFields
                ? `Hide the ${st.fc.totals.neither} field${st.fc.totals.neither === 1 ? "" : "s"} with nothing yet`
                : `Show the ${st.fc.totals.neither} field${st.fc.totals.neither === 1 ? "" : "s"} with nothing yet (${st.fc.totals.neitherAcres.toLocaleString()} ac)`}
            </button>
          ) : null}
          </details>
          {/* ⚠ AN APPRAISAL WE CANNOT PLACE IS PRODUCTION THE LIST DOES NOT MENTION, so it is
              named here rather than dropped. The two lists are different facts: farm-level is
              production we HAVE and merely cannot split per field; unplaced is production
              nothing can attribute, which needs a human. */}
          {/* ⚠ A COMBINE PASS NOTHING CAN PLACE IS STILL PRODUCTION THAT EXISTS. Measured
              2026-10-01, 181 of 271 such passes book-wide land here, because their John Deere
              field has never had a gridded pass and so nothing ties it to a CLU. Dropping them
              is how real harvest stays invisible. */}
          {st.fc.unplacedPassCount ? (
            <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 8 }}>
              <b>{st.fc.unplacedPassCount} combine pass{st.fc.unplacedPassCount === 1 ? "" : "es"}</b> on this
              farm could not be tied to an insured field:{" "}
              {st.fc.unplacedPasses.slice(0, 4).map((p) => (p.fieldName || p.fieldId || "?") + " " + p.acres + " ac").join(" · ")}
              {st.fc.unplacedPassCount > 4 ? " and " + (st.fc.unplacedPassCount - 4) + " more" : ""}
              {st.fc.unplacedPasses[0] ? " — " + st.fc.unplacedPasses[0].why + "." : ""}
            </div>
          ) : null}
          {st.fc.farmLevelCount ? (
            <div style={{ fontSize: 11.5, color: "#5a6a5a", marginTop: 8 }}>
              <b>{st.fc.farmLevelCount} appraisal line{st.fc.farmLevelCount === 1 ? "" : "s"} cover several fields each:</b>
              {st.fc.farmLevelAppraisals.map((u, i) => (
                <div key={i} style={{ marginTop: 2 }}>
                  • {u.fsn ? `farm ${u.fsn}` : u.unitKey ? `unit ${u.unitLabel || u.unitKey}` : "farm (none given)"}{u.field ? `, worksheet line ${u.field}` : ""} — {u.acres != null ? u.acres.toFixed(1) + " ac" : "acreage not stated"}
                  {u.quantity != null ? ` · ${Math.round(u.quantity).toLocaleString()}` : ""}. {u.why}
                </div>
              ))}
            </div>
          ) : null}
          {st.fc.unplacedCount ? (
            <div style={{ fontSize: 11.5, color: "#8a3324", marginTop: 8 }}>
              <b>{st.fc.unplacedCount} appraisal line{st.fc.unplacedCount === 1 ? "" : "s"} not tied to a field</b>
              {" "}<span style={{ color: "#5a6a5a" }}>(grain lines are counted at their unit; silage-insured bushel lines are not counted):</span>
              {st.fc.unplacedAppraisals.map((u, i) => (
                <div key={i} style={{ marginTop: 2 }}>
                  • {u.fsn ? `farm ${u.fsn}` : u.unitKey ? `unit ${u.unitLabel || u.unitKey}` : "farm (none given)"}{u.field ? `, worksheet line ${u.field}` : ""} — {u.acres != null ? u.acres.toFixed(1) + " ac" : "acreage not stated"}
                  {u.quantity != null ? ` · ${Math.round(u.quantity).toLocaleString()}` : ""}. {u.why}
                  {u.tiedWith && u.tiedWith.length ? ` Same acres as ${u.tiedWith.join(", ")}.` : ""}
                  {u.likelyFields ? ` ${u.likelyFields.charAt(0).toUpperCase() + u.likelyFields.slice(1)}.` : ""}
                  {u.headerConflict ? <span style={{ fontWeight: 700 }}>{` ⚠ ${u.headerConflict.charAt(0).toUpperCase() + u.headerConflict.slice(1)}.`}</span> : null}
                </div>
              ))}
            </div>
          ) : null}
          {/* ⚠ THE METHOD IS KEPT AND MOVED, never deleted — it is how a reader checks whether a
              placement is trustworthy. But it is the same six lines on every farm, and on a phone
              it pushed the findings off the screen. Behind a summary it costs one tap and no
              honesty. It only shows at all where an appraisal was actually placed. */}
          {st.fc.rows.some((r) => r.appraised) ? (
            <details style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 11, color: "#60735f", cursor: "pointer" }}>How an appraisal is tied to a field</summary>
              <div style={{ fontSize: 11, color: "#60735f", marginTop: 4, lineHeight: 1.5 }}>
                An appraisal is tied to a field by farm number plus tract plus field number where
                the adjuster gave all three, and otherwise by the farm number plus an exact,
                unique acreage — because where no tract is given the adjuster's "field 1" is the
                line number on their own worksheet, which matched FSA's field number on none of
                the rows we could check against acreage.
              </div>
            </details>
          ) : null}
        </div>
      ) : null}

    {/* ── EVERY PLACE A BUSHEL COULD HAVE COME FROM ──────────────────────────
        Present or not, each with the RMA record type the report files it under. An absent
        channel is LISTED — "no scale tickets on file" and "scale tickets: 0 bu" are different
        statements, and a channel left off the list reads as the second one. */}
    <div style={card}>
      <div style={h}>Production sources</div>
      <div>
        {srcs.rows.map((r) => (
          <div key={r.kind} style={{ display: "flex", alignItems: "flex-start", gap: 9, padding: "9px 0", borderTop: "1px solid #eef3ee" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", marginTop: 5, flexShrink: 0, background: r.inUse ? (r.kind === "jd_yield" ? "#c49a2a" : "#2d6a2d") : "transparent", border: r.inUse ? "none" : "1px solid #cfdccf" }} />
            <span style={{ flexGrow: 1, minWidth: 0 }}>
              <span style={{ fontSize: 12.5, fontWeight: r.inUse ? 700 : 400, display: "block" }}>{r.label}</span>
              {/* ⚠ THE TWO CODES RMA DOES NOT RESOLVE RENDER AS A QUESTION, in the amber this
                  app uses for "ask" — never as a code we picked. A code on a federal form that
                  nobody chose is the invented-explanation failure rule zero opens with. */}
              {r.needsHuman
                ? <span style={{ fontSize: 10.5, color: "#8a6d1f" }}>{(r.candidates || []).join(" or ")} — {r.note}</span>
                : <span style={{ fontSize: 10.5, color: "#6a7a6a" }}>{recordTypeLabel(r.code) || r.note}</span>}
            </span>
            <span style={{ textAlign: "right", flexShrink: 0 }}>
              <span style={{ display: "block", fontFamily: r.present ? "monospace" : "inherit", fontSize: r.present ? 13 : 12, fontWeight: r.present ? 700 : 400, color: r.present ? "#1a2e1a" : "#60735f" }}>
                {r.present ? qty(r.quantity, r.unit) : r.absentText}
              </span>
              <span style={{ display: "inline-block", marginTop: 3, fontVariantNumeric: "tabular-nums", fontSize: 10.5, fontWeight: 700, border: "1px solid " + (r.needsHuman ? "#e8d8a8" : "#dde8d8"), borderRadius: 4, padding: "2px 5px", color: r.needsHuman ? "#8a6d1f" : "#5a6a5a", background: "#f7faf7" }}>
                {r.code || (r.candidates || []).join(" / ")}
              </span>
            </span>
          </div>
        ))}
      </div>
      {/* Why the one in use won, stated rather than implied. */}
      <div style={{ fontSize: 11, color: "#6a7a6a", marginTop: 9, lineHeight: 1.45 }}>
        {/* ⚠ FOUR CASES, NOT THREE. The appraisal-only arm was missing, so a farm holding an
            appraisal and nothing else fell through to a sentence about the monitor — and
            before monitorChosen required a monitor to EXIST, the last arm was unreachable
            entirely. Rogers Farms read "the monitor is what would be reported" two lines
            under his own John Deere row saying "no machine data". */}
        {srcs.monitorChosen && !srcs.documentedAvailable
          ? "Scale tickets and farm records would outrank the monitor. With none on file, the monitor is what would be reported."
          : srcs.documentedAvailable
            ? "Farm records outrank the yield monitor — they measure the same harvest, so only one is reported."
            : srcs.appraisalPresent
              ? "The appraisal is the only production on file — it is what would be reported, for the acres it covers. Scale tickets or farm records would outrank it on any acre that was harvested."
              : "Nothing is on file for this crop year yet."}
      </div>
    </div>
    <div style={card}>
      <div style={h}>What we hold for a production report</div>

      {/* The carrier may already have it. Say that first — a report already filed is not
          work outstanding, and that is 21 of 129 CY2026 wheat farms. */}
      {ev.carrier.alreadyReported ? (
        <div style={{ fontSize: 12.5, color: "#2d6a2d", fontWeight: 700, marginBottom: 10 }}>
          ✓ The carrier already holds production for CY{ev.cropYear} {ev.crop} — {qty(ev.carrier.quantity, ev.unit)} across {ev.carrier.reportedRows} row{ev.carrier.reportedRows === 1 ? "" : "s"}.
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: "#8a6d1f", fontWeight: 700, marginBottom: 10 }}>
          Production for CY{ev.cropYear} {ev.crop} is NOT yet on the carrier's APH
          {ev.carrier.actualRows > 0 ? ` (${ev.carrier.actualRows} actual row${ev.carrier.actualRows === 1 ? "" : "s"} carrying no production)` : ""}.
        </div>
      )}

      {/* ── WHICH PRODUCTION WOULD BE FILED ──────────────────────────────────
          Kevin, 2026-10-01: "we should be able to choose which production to use
          either Scale or Yield Monitor."

          ⚠ THE TWO ARE THE SAME BUSHELS, MEASURED TWICE — Lawnel's 33,426 of scale
          tickets and 32,572 of monitor are ONE Livingston wheat harvest over the
          same 399 acres. The lane table below shows both and, until now, said
          nothing about which one you would report; adding them files roughly
          double the crop, which erases an indemnity the grower is owed. The rule
          and its sourcing are in src/claims/productionReportLine.js. */}
      {rep.ok && (
        <div style={{ background: "#f4f8f2", border: "1px solid #dde8d8", borderRadius: 8, padding: 10, marginBottom: 12 }}>
          {/* ⚠ A PICKER WITH ONE OPTION IS NOT A CHOICE, IT IS THE SOURCES CARD AGAIN. Where
              only one basis exists the card above has already named it, its figure and its RMA
              code, and said what would have outranked it — so a single button here is the
              third printing of one number. With TWO it earns its place immediately: that is
              the case where the channels disagree and somebody has to decide. */}
          {rep.bases.length > 1 && (<>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>
            Which production to report
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {rep.bases.map((b) => {
              const on = b.key === rep.basis;
              return (
                <button key={b.key} type="button" onClick={() => setBasis(b.key)}
                  title={b.why}
                  style={{ textAlign: "left", cursor: "pointer", borderRadius: 8, padding: "7px 11px",
                    border: on ? "2px solid #2d6a2d" : "1px solid #cfe0cb",
                    background: on ? "#eaf4e8" : "#fff", color: "#1a2e1a" }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>{on ? "● " : "○ "}{b.label}</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {qty(b.quantity, b.unit)}
                  </div>
                  <div style={{ fontSize: 11, color: "#5a6a5a" }}>
                    {b.codes.map((c) => `${c} — ${recordTypeLabel(c) || ""}`).join(" · ")}
                  </div>
                </button>
              );
            })}
          </div>
          </>)}
          {/* ⚠ defaultReason WAS PRINTED HERE AND IS NOW THE SOURCES CARD'S FOOTER — the same
              sentence, once. A default must still be printed AS one, which it is: the sources
              card names what would have outranked the chosen channel and says that nothing
              did. Do not restore this line without removing that one. */}
          {/* THE GAP, because 2.6% and 40% are different conversations. */}
          {rep.spread && (
            <div style={{ fontSize: 11.5, color: rep.spread.pct >= 10 ? "#8a6d1f" : "#5a6a5a", marginBottom: 6 }}>
              {rep.spread.pct}% apart — {rep.chosen.label} reads{" "}
              {rep.spread.difference >= 0 ? "+" : ""}{Math.round(rep.spread.difference).toLocaleString()} {rep.unit || ""} against{" "}
              {rep.spread.otherLabel}. Neither figure is corrected here.
            </div>
          )}
          {/* An empty bold div still takes its line box, so the wrapper is gated too —
              otherwise removing the text leaves a gap that reads as a missing figure. */}
          {rep.appraised != null && (
          <div style={{ fontSize: 12.5, color: "#1a2e1a", fontWeight: 700 }}>
            {/* ⚠ THE APPRAISAL IS THE PART THIS LINE UNIQUELY CARRIES. The harvested figure
                and its source lead the page and are repeated at the fill button, which is the
                point of action — a third printing between them was two of the four times
                78,057 appeared on one screen. An appraisal is a DIFFERENT quantity (production
                on acres that were never harvested, its own line on the form).
                ⚠ SINCE 2026-10-07 IT IS ALSO ON THE CLAIM-STANDS CARD, and this line is not a
                duplicate of that one: the card answers "what is the evidence against the
                guarantee", this answers "what would go on the FORM" — two lines, two RMA
                record types. Kevin's card read "Production —" above "No claim indicated" with
                298,043 appraised bushels on the same screen, so the evidence had to sit beside
                the verdict; the filing statement still needs both figures to be a filing
                statement. */}
            {rep.appraised != null
              ? <>Would file {qty(rep.quantity, rep.unit)} harvested · {qty(rep.appraised, rep.unit)} appraised (unharvested, its own line)</>
              : null}
          </div>
          )}
          {rep.refusals.map((r, i) => (
            <div key={i} style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>⚠ {r}</div>
          ))}
          {/* ⚠ NO FILL BUTTON, AND THE REASON IS STATED RATHER THAN LEFT AS AN ABSENCE.
              Measured 2026-10-01: farmer_documents holds 304 rows and every one is an
              acreage_report — there is not a production form in storage to stamp. */}
          {/* ⚠ THE FILL BUTTON LIVES BELOW, AND THE SENTENCE ABOVE IT IS A SCOPE NOTE, NOT
              A STORAGE CLAIM. `stampBlocked` used to say "no production form is on file" —
              a statement about `farmer_documents` made by a rule that never queries it, true
              the day it was measured and false the next. The panel asks. */}
          <div style={{ fontSize: 11.5, color: "#6a7a6a", marginTop: 6 }}>{rep.stampBlocked}</div>
          <ProductionFillPanel farmer={farmer} crop={crop} cropYear={cropYear} basis={rep.basis} qty={qty} />
        </div>
      )}

      {/* ── THE LANES, EACH WITH ITS RMA CODE ── */}
      {/* ⚠ ONE LANE IS NOT A TABLE, IT IS THE SAME SENTENCE TWICE. Where a single channel
          carries the production, the card above has already named it, its quantity and its RMA
          record type — so a four-column table restating them is the third printing of 78,057 on
          one screen. With TWO OR MORE lanes it earns its place immediately: that is the case
          where the channels can disagree and the reader has to choose between them.

          ⚠⚠ AND THE SUPPRESSION USED TO FALL THROUGH TO THE ZERO BRANCH, so a farm with
          EXACTLY ONE lane printed "No production evidence of any kind on file — no scale
          tickets, no machine yield, no appraisal, nothing typed" directly beneath a card
          reading 26,911 bu of scale tickets. Measured on ANDERSON FARMS DAIRY, CY2026 wheat,
          2026-10-02. ONE is not ZERO, and the ternary had only two arms for three states. The
          figures were right and the conclusion contradicted them, which is the failure mode
          this panel exists to prevent — found by RENDERING it; every static checker was green
          and the sentence is correct code for the case it was written for. */}
      {ev.lanes.length > 1 ? (
        <div style={{ overflowX: "auto", marginBottom: 12 }}>
          <table style={{ width: "100%", minWidth: 520, borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr style={{ textAlign: "left", color: "#5a6a5a" }}>
              <th style={{ padding: "4px 8px 4px 0" }}>Evidence</th>
              <th style={{ padding: "4px 8px", textAlign: "right" }}>Quantity</th>
              <th style={{ padding: "4px 8px" }}>RMA record type</th>
              <th style={{ padding: "4px 0" }}>Detail</th>
            </tr></thead>
            <tbody>
              {ev.lanes.map((l) => (
                <tr key={l.kind} style={{ borderTop: "1px solid #eef3ee" }}>
                  <td style={{ padding: "6px 8px 6px 0", fontWeight: 700 }}>{l.label}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{qty(l.quantity, l.unit)}</td>
                  <td style={{ padding: "6px 8px", color: l.needsHuman ? "#8a6d1f" : "#2d6a2d", fontWeight: 600 }}>
                    {/* ⚠ A LANE WHOSE CODE WE CANNOT DETERMINE ASKS, rather than showing a
                        default. A code we picked is a statement the grower did not make. */}
                    {l.needsHuman ? `${l.candidates.join(" or ")} — ask` : `${l.code} — ${recordTypeLabel(l.code) || ""}`}
                  </td>
                  <td style={{ padding: "6px 0", color: "#6a7a6a" }}>
                    {l.tickets != null && `${l.tickets} ticket${l.tickets === 1 ? "" : "s"}, moisture on ${l.withMoisture}`}
                    {/* ⚠ BOTH BASES, ALWAYS, AND THE FACTOR BETWEEN THEM. The quantity column
                        carries the RMA figure because that is what goes on the report; the
                        elevator's own net is printed beside it because that is what the
                        grower's settlement sheets say, and a panel that showed only one would
                        read as contradicting whichever paper the reader is holding. The
                        shrink is named so the gap is checkable rather than mysterious. */}
                    {l.basis === "rma_net" && l.elevatorNet != null && (
                      <div style={{ fontSize: 11.5, marginTop: 2 }}>
                        RMA basis — elevator net {Math.round(l.elevatorNet).toLocaleString()} {l.unit || ""} re-shrunk at{" "}
                        {l.shrinkPerPt}%/pt to {l.baseMoisture}% ({l.rmaNet - l.elevatorNet >= 0 ? "+" : ""}
                        {Math.round(l.rmaNet - l.elevatorNet).toLocaleString()})
                        {l.shrinkable < l.tickets && (
                          <span style={{ color: "#8a6d1f" }}> · {l.tickets - l.shrinkable} load
                            {l.tickets - l.shrinkable === 1 ? "" : "s"} missing gross/moisture, elevator net used for {l.tickets - l.shrinkable === 1 ? "it" : "those"}</span>
                        )}
                      </div>
                    )}
                    {l.counties && l.counties.map((c) => `${c.county} ${Math.round(c.quantity).toLocaleString()}`).join(" · ")}
                    {l.dropped > 0 && <span style={{ color: "#8a6d1f" }}> · {l.dropped} row{l.dropped === 1 ? "" : "s"} not valued</span>}
                    {/* ⚠ A NARROWING THE READER CANNOT SEE IS WORSE THAN THE OVER-COUNT IT
                        FIXES. The machine figure is now scoped to CLUs insured for THIS crop
                        this year, so it is smaller than the raw monitor total — and a reader
                        comparing it against Operations Center must be told why. Both facts,
                        separately: ground we can prove is out of scope, and ground the
                        attribution could not place (which is KEPT, not dropped). */}
                    {l.kind === "jd_yield" && l.cluScoped && (l.offBookAcres > 0 || l.noCluAcres > 0) && (
                      <div style={{ fontSize: 11.5, marginTop: 2, color: "#6a7a6a" }}>
                        Scoped to the {l.insuredClus} CLU{l.insuredClus === 1 ? "" : "s"} insured for this crop this year.
                        {l.offBookAcres > 0 && <> <span style={{ color: "#8a6d1f" }}>{l.offBookAcres} ac on {l.offBookRows} row{l.offBookRows === 1 ? "" : "s"} excluded — the machine ran them, but no {ev.crop} policy covers that ground this year (custom work, a neighbour's field, or the grower's own acres insured to another crop).</span></>}
                        {l.noCluAcres > 0 && <> <span style={{ color: "#8a6d1f" }}>{l.noCluAcres} ac could not be matched to a CLU at all and are still counted — a gap in our attribution is not evidence the acres are uninsured.</span></>}
                      </div>
                    )}
                    {l.kind === "jd_yield" && !l.cluScoped && (
                      <div style={{ fontSize: 11.5, marginTop: 2, color: "#8a6d1f" }}>
                        Not scoped to insured ground — no CLU-level field records are on file for this crop
                        and year, so every acre the machine ran is counted, including any that are not insured.
                      </div>
                    )}
                    {l.typed && "typed"}
                    {l.needsHuman && <div style={{ fontSize: 11.5 }}>{l.why}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : ev.lanes.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "#8a6d1f", marginBottom: 12 }}>
          No production evidence of any kind on file — no scale tickets, no machine yield, no
          appraisal, nothing typed. That is a document-collection gap, not something this
          screen can compute around.
        </div>
      ) : null}

      {/* ── DISAGREEMENTS: NAMED, NOT RESOLVED ── */}
      {ev.disagreements.length > 0 && (
        <div style={{ background: "#fff8e8", border: "1px solid #e8d8a8", borderRadius: 8,
          padding: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#8a6d1f", marginBottom: 4 }}>
            {ev.disagreements.length} disagreement{ev.disagreements.length === 1 ? "" : "s"} between channels — neither figure is corrected here
          </div>
          {ev.disagreements.map((d, i) => (
            <div key={i} style={{ fontSize: 12, color: "#6a5a2a", marginTop: 4 }}>
              <strong>{d.between.join(" vs ")}</strong>{d.gapPct ? ` — ${d.gapPct}% apart` : ""}:{" "}
              {Object.entries(d.values).map(([k, v]) => `${k} ${Math.round(v).toLocaleString()}`).join(" · ")}
              <div style={{ fontSize: 11.5, color: "#8a7a4a" }}>{d.note}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── PER COUNTY ──
          ⚠ EIGHT COLUMNS DO NOT FIT A PHONE, AND `width: 100%` MEANT IT NEVER TRIED.

          The wrapper has had `overflowX: auto` since this panel was written, but a table
          set to `width: 100%` with no `minWidth` has nothing to overflow — it COMPRESSES
          to the viewport instead. At 390px that shredded every heading to one or two
          characters a line ("b u / a c", "Ap prai sed"), wrapped LIVINGSTON to three
          lines and split "1,183.2" across two. Kevin, 2026-10-06: "this display is
          pretty ugly now, I think the layout was ruined to be mobile friendly."

          Adding the Appraised-ac column earlier the same day took it from seven columns
          to eight and is what tipped it over.

          A phone gets ONE CARD PER COUNTY instead — the same figures, stacked and
          labelled, with nothing to scroll sideways. The table is kept for real width,
          where eight columns read fine and the column alignment is the point. Scrolling
          the table sideways was the other option and is rejected: this repo already
          records a horizontal-overflow complaint, and a figure you have to drag into
          view is a figure that gets missed. */}
      {isMobile ? (
        <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
          {ev.counties.map((c) => {
            /* ⚠ THE PHONE READS THE SAME RULE THE TABLE DOES. Kevin, 2026-10-07: "Looks
               like the claims manager changes aren't on mobile." Every figure added that
               day — planted acres, the bushel and dollar guarantee, bu/ac, over/under —
               had gone into the wide table only, because the derivation was an inline
               IIFE inside one table row with nowhere for this layout to call it from. */
            const cg = countyGuaranteeRead(c, claimRead && claimRead.guaranteeByCounty);
            return (
            <div key={c.county} style={{ border: "1px solid #e3ece3", borderRadius: 8,
                                        padding: "9px 11px", background: "#fcfdfc", minWidth: 0 }}>
              {/* The county and what we can say about it — the two things that decide
                  whether the rest of the card needs reading at all. */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                            gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 800, fontSize: 13.5, color: "#1a2e1a" }}>{c.county}</div>
                <div style={{ fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                  color: c.status === "stated" ? "#2d6a2d" : c.status === "unsettled" ? "#a33" : "#8a6d1f" }}>
                  {c.status === "stated" ? "stated" : c.status === "unsettled" ? "UNSETTLED" : "evidence, not stated"}
                </div>
              </div>
              {/* ⚠ PLANTED, NOT INSURED — the same switch the table made. No bushel was ever
                  grown on a prevent-planted acre, so an insured figure beside an appraised
                  one invites the reader to subtract and find a shortfall that is prevent
                  plant: MULLIGAN's LIVINGSTON is 1,183.2 insured against 1,155.5 appraised,
                  which reads 27.7 ac short and is exactly his PP. The phone has no hover, so
                  the insured figure and the PP split are PRINTED rather than put on a title. */}
              <div style={{ fontSize: 11.5, color: "#5a6a5a", marginTop: 2 }}>
                Planted <b style={{ color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>
                  {(c.plantedAcres != null ? c.plantedAcres : c.insuredAcres).toLocaleString()}</b> ac
                {c.ppAcres > 0.05
                  ? <span style={{ color: "#60735f" }}> · {c.insuredAcres.toLocaleString()} insured less {c.ppAcres.toLocaleString()} PP</span>
                  : <span style={{ color: "#60735f" }}> · {c.insuredAcres.toLocaleString()} insured, no PP</span>}
                {c.uiAcres > 0.05 && <span style={{ color: "#60735f" }}> · {c.uiAcres.toLocaleString()} ac uninsurable</span>}
              </div>
              {/* Three sources of production evidence, one row each, acres then bushels.
                  Ordered appraisal → monitor → stated because that is the order they
                  arrive in, and a row keeps its dash rather than vanishing: an absent
                  source is a fact about the claim, which is this panel's whole subject. */}
              <div style={{ marginTop: 7, borderTop: "1px solid #eef3ee", paddingTop: 6,
                            display: "grid", gridTemplateColumns: "minmax(0, auto) minmax(0, 1fr) minmax(0, 1fr)",
                            gap: "4px 10px", fontSize: 12, alignItems: "baseline" }}>
                <div style={{ color: "#5a6a5a" }}>Appraised</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                  fontWeight: c.appraised ? 700 : 400, color: c.appraised ? "#1a2e1a" : "#aab5aa" }}>
                  {c.appraised ? c.appraised.acres.toLocaleString() + " ac" : "—"}
                </div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#6a7a6a" }}>
                  {c.appraised ? Math.round(c.appraised.quantity).toLocaleString() + " bu" : "—"}
                </div>

                <div style={{ color: "#5a6a5a" }}>Monitor</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                  fontWeight: c.monitorYield != null ? 700 : 400,
                  color: c.monitorYield != null ? "#1a4a1a" : "#aab5aa" }}>
                  {c.monitorYield != null ? c.monitorYield.toLocaleString() + " bu/ac" : "—"}
                </div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#6a7a6a" }}>
                  {c.jd ? Math.round(c.jd.quantity).toLocaleString() + " bu" : "—"}
                </div>

                {/* ⚠ A STATED ZERO PRINTS AS 0 AND AN ABSENT ONE PRINTS AS A DASH — they
                    are different statements, and read as 0 an absence is a total loss. */}
                <div style={{ color: "#5a6a5a" }}>Stated</div>
                <div />
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                  fontWeight: c.stated != null ? 700 : 400,
                  color: c.stated != null ? "#1a2e1a" : "#aab5aa" }}>
                  {c.stated == null ? "—" : Math.round(c.stated).toLocaleString() + " bu"}
                </div>
              </div>

              {/* ── THE GUARANTEE AND THE COMPARISON — the half the phone was missing ──
                  Kevin: "then it would all be in one place: Insured acres, guarantees, what
                  was appraised, are they under or over by county." The dollar figure is
                  RMA's own liability; the bushel figure is that liability over the projected
                  price, which is what the SOI calls the Marketable Guarantee. */}
              <div style={{ marginTop: 7, borderTop: "1px solid #eef3ee", paddingTop: 6,
                            display: "grid", gridTemplateColumns: "minmax(0, auto) minmax(0, 1fr)",
                            gap: "4px 10px", fontSize: 12, alignItems: "baseline" }}>
                <div style={{ color: "#5a6a5a" }}>Guarantee</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                  fontWeight: cg.gBu > 0 || cg.gTon > 0 ? 700 : 400,
                  color: cg.gBu > 0 || cg.gTon > 0 ? "#1a2e1a" : "#aab5aa" }}>
                  {cg.gBu > 0 ? Math.round(cg.gBu).toLocaleString() + " " + (ev.unit || "bu")
                    : cg.gTon > 0 ? Math.round(cg.gTon).toLocaleString() + " t" : "—"}
                  {cg.liability > 0 && <span style={{ color: "#6a7a6a", fontWeight: 400 }}>
                    {" · $" + Math.round(cg.liability).toLocaleString()}</span>}
                </div>

                <div style={{ color: "#5a6a5a" }}>Prod / ac</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                  fontWeight: cg.prodPerAcre != null ? 700 : 400,
                  color: cg.prodPerAcre != null ? "#1a2e1a" : "#aab5aa" }}>
                  {cg.prodPerAcre != null
                    ? cg.prodPerAcre.toFixed(1) + " " + (ev.unit || "bu") + "/ac"
                    : "—"}
                </div>

                <div style={{ color: "#5a6a5a" }}>Over / under</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: cg.ou.ok ? 800 : 400,
                  color: !cg.ou.ok ? "#aab5aa" : cg.ou.under ? "#a33" : "#2d6a2d" }}>
                  {cg.ou.ok
                    ? (cg.ou.diff >= 0 ? "+" : "−") + Math.abs(Math.round(cg.ou.diff)).toLocaleString() + " " + (ev.unit || "bu")
                    : "—"}
                </div>
              </div>
              {/* ⚠ A DASH IS A REFUSAL WITH A REASON AND A PHONE HAS NO HOVER TO PUT IT ON,
                  so it is printed. Comparing a part-county production against the whole
                  county's guarantee values the unmeasured acres at ZERO, which is the largest
                  shortfall a policy can show — the Rogers Farms inversion, per county. */}
              {!cg.ou.ok && (
                <div style={{ fontSize: 11, color: "#8a6d1f", marginTop: 5, lineHeight: 1.45 }}>
                  No over/under: {cg.guaranteeWhy || cg.ou.why}.
                </div>
              )}
            </div>
            );
          })}
        </div>
      ) : (
      <div style={{ overflowX: "auto", marginBottom: 12 }}>
        {/* The wide layout — eight columns read fine with room for them. */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead><tr style={{ textAlign: "left", color: "#5a6a5a" }}>
            <th style={{ padding: "4px 8px 4px 0" }}>County</th>
            {/* ⚠ PLANTED, NOT INSURED. Kevin, 2026-10-07: "instead of insured acres, it
                should be planted acres. We dont care about Prevent Plant when it comes to
                production reporting, just display planted acres." No bushel was ever grown
                on a prevent-planted acre, so an insured figure beside an appraised one
                invites the reader to subtract and find a shortfall that is prevent plant —
                MULLIGAN's LIVINGSTON is 1,183.2 insured against 1,155.5 appraised, which
                reads 27.7 ac short and is exactly his PP. The insured figure and the PP
                split are on the hover, so nothing is hidden. */}
            <th style={{ padding: "4px 8px", textAlign: "right" }}
                title="Insured acres less prevent plant. Nothing grows on a prevent-planted acre, so it can carry neither an appraisal nor a ticket.">Planted ac</th>
            <th style={{ padding: "4px 8px", textAlign: "right" }}>Stated</th>
            <th style={{ padding: "4px 8px", textAlign: "right" }}>JD</th>
            {/* Monitor yield — see the note on the cell below for why the denominator is
                the machine's own acres and why it is withheld on a partial county. */}
            <th style={{ padding: "4px 8px", textAlign: "right" }} title="John Deere bushels over the acres the monitor covered. Shown only where the combine accounts for every insured field in that county.">bu/ac</th>
            {/* ⚠ APPRAISED ACRES AND APPRAISED BUSHELS ARE DIFFERENT QUESTIONS, and the
                acres half was computed and never rendered. Kevin, 2026-10-06: "I want to see
                insured acres by county/unit. Appraised acres by county/unit." The acres say
                whether the adjuster covered the ground; the bushels say what he found on it.
                A county can be fully appraised and short of bushels, or part-appraised with a
                large number — and with only the bushel column on screen those two read the
                same. `c.appraised.acres` has been on the object since productionEvidence.js
                was written; this prints it. */}
            <th style={{ padding: "4px 8px", textAlign: "right" }}>Appraised ac</th>
            <th style={{ padding: "4px 8px", textAlign: "right" }}>Appraised</th>
            {/* ⚠ THE GUARANTEE BELONGS BESIDE THE PRODUCTION IT IS READ AGAINST. Kevin:
                "then it would all be in one place: Insured acres, guarantees, what was
                appraised, are they under or over by county." The dollar column is the
                LIABILITY — RMA's own revenue guarantee — and the bushel column is that
                liability over the projected price, which is what the SOI calls the
                Marketable Guarantee. */}
            <th style={{ padding: "4px 8px", textAlign: "right" }} title="Liability ÷ projected price — the SOI's Marketable Guarantee for this county.">Guar</th>
            {!isMobile && <th style={{ padding: "4px 8px", textAlign: "right" }} title="RMA's liability for this county — the dollar guarantee.">Guar $</th>}
            {/* Kevin: "also, calculate the bushels per acre." Over PLANTED acres, which is
                the only denominator a production figure has — and the same one the guarantee
                beside it is read against, so the two columns compare directly. */}
            <th style={{ padding: "4px 8px", textAlign: "right" }} title="The production this county has on file, over its planted acres.">Prod / ac</th>
            <th style={{ padding: "4px 8px", textAlign: "right" }} title="Production against the guarantee for this county. Withheld where the production on file does not cover the county's planted acres — the unmeasured acres would otherwise count as zero.">Over / under</th>
            <th style={{ padding: "4px 0" }}>Status</th>
          </tr></thead>
          <tbody>
            {ev.counties.map((c) => (
              <tr key={c.county} style={{ borderTop: "1px solid #eef3ee" }}>
                <td style={{ padding: "6px 8px 6px 0", fontWeight: 700 }}>{c.county}</td>
                <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                  title={(c.ppAcres > 0.05
                    ? c.insuredAcres.toLocaleString() + " insured ac less " + c.ppAcres.toLocaleString() + " prevent plant"
                    : c.insuredAcres.toLocaleString() + " insured ac, no prevent plant")
                    + (c.uiAcres > 0.05 ? " (" + c.uiAcres.toLocaleString() + " ac uninsurable, already outside the insured figure)" : "")}>
                  {(c.plantedAcres != null ? c.plantedAcres : c.insuredAcres).toLocaleString()}
                </td>
                {/* ⚠ A STATED ZERO PRINTS AS 0 AND AN ABSENT ONE PRINTS AS A DASH. They are
                    different statements: 0 is "nothing came off these acres", absent is "we
                    have not said". Read as 0, an absence is a total loss on that county. */}
                <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                  fontWeight: c.stated != null ? 700 : 400, color: c.stated != null ? "#1a2e1a" : "#aab5aa" }}>
                  {c.stated == null ? "—" : Math.round(c.stated).toLocaleString()}
                </td>
                <td style={{ padding: "6px 8px", textAlign: "right", color: "#6a7a6a", fontVariantNumeric: "tabular-nums" }}>
                  {c.jd ? Math.round(c.jd.quantity).toLocaleString() : "—"}
                </td>
                {/* ⚠ A DASH HERE IS NOT "NO DATA" — it is "not every insured field in this
                    county has been combined yet", which is the question this whole panel
                    exists to raise. An average over the harvested half would answer it with
                    a number instead. The title says which, so the dash is never mute. */}
                <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                  fontWeight: c.monitorYield != null ? 700 : 400,
                  color: c.monitorYield != null ? "#1a4a1a" : "#aab5aa" }}
                  title={c.monitorYield != null
                    ? "Monitor yield: " + c.jd.quantity.toLocaleString() + " bu over " + c.jd.acres + " ac the combine covered (not the " + c.insuredAcres + " insured ac — CLU matching trims headlands, so those bushels are not in the total either)"
                    : (c.machineMissingFields ? c.machineMissingFields + " insured field(s) in this county have no monitor data yet" : "no monitor data for this county")}>
                  {c.monitorYield != null ? c.monitorYield.toLocaleString() : "—"}
                </td>
                {/* The share of the county's insured acres the adjuster actually walked.
                    Printed beside the acres it is a share OF, so "806 of 1,183" is readable
                    without arithmetic. A county with no appraisal at all reads "—", never 0 —
                    nobody appraised nothing, we simply have nothing. */}
                <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                  fontWeight: c.appraised ? 700 : 400, color: c.appraised ? "#1a2e1a" : "#aab5aa" }}
                  title={c.appraised
                    ? c.appraised.acres.toLocaleString() + " appraised ac of " + c.insuredAcres.toLocaleString() + " insured ac in this county"
                    : "no appraisal on file for this county"}>
                  {c.appraised ? c.appraised.acres.toLocaleString() : "—"}
                </td>
                <td style={{ padding: "6px 8px", textAlign: "right", color: "#6a7a6a", fontVariantNumeric: "tabular-nums" }}>
                  {c.appraised ? Math.round(c.appraised.quantity).toLocaleString() : "—"}
                </td>
                {(() => {
                  // ⚠ ONE RULE, BOTH LAYOUTS. This used to be an inline derivation right here,
                  // which is exactly why the phone card never got the guarantee, the bu/ac or
                  // the over/under — there was nowhere for the second renderer to call it from.
                  const { g, covers, gBu, gTon, ou } = countyGuaranteeRead(c, claimRead && claimRead.guaranteeByCounty);
                  return (<>
                    <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                      fontWeight: gBu > 0 || gTon > 0 ? 700 : 400, color: gBu > 0 || gTon > 0 ? "#1a2e1a" : "#aab5aa" }}
                      title={g && !covers
                        ? "the priced coverage lines in this county cover "
                          + Math.round(g.guaranteedAcres).toLocaleString() + " of its "
                          + Math.round(c.insuredAcres).toLocaleString()
                          + " insured ac, so no county guarantee is shown — a part-acreage total would read as the whole county's"
                        : undefined}>
                      {gBu > 0 ? Math.round(gBu).toLocaleString() : gTon > 0 ? Math.round(gTon).toLocaleString() + " t" : "—"}
                    </td>
                    {!isMobile && (
                      <td style={{ padding: "6px 8px", textAlign: "right", color: "#6a7a6a", fontVariantNumeric: "tabular-nums" }}>
                        {covers && g.liability > 0 ? "$" + Math.round(g.liability).toLocaleString() : "—"}
                      </td>
                    )}
                    {/* ⚠ THE SAME PRODUCTION THE OVER/UNDER USED, over the same planted acres —
                        so a reader checking one against the other finds them consistent. It is
                        BLANK on exactly the rows where the comparison is withheld, because a
                        per-acre figure off a part-county production is the same inversion in a
                        smaller number: Rogers' 308 appraised acres over 1,108 planted reads
                        54 bu/ac on a crop running 196. */}
                    <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                      fontWeight: ou.ok ? 700 : 400, color: ou.ok ? "#1a2e1a" : "#aab5aa" }}
                      title={ou.ok
                        ? Math.round(ou.production).toLocaleString() + " " + (ev.unit || "bu") + " over "
                          + (c.plantedAcres != null ? c.plantedAcres : c.insuredAcres).toLocaleString() + " planted ac (" + ou.basis + ")"
                        : ou.why}>
                      {ou.ok && (c.plantedAcres || 0) > 0.05
                        ? (ou.production / c.plantedAcres).toFixed(1)
                        : "—"}
                    </td>
                    {/* ⚠ A DASH HERE IS A REFUSAL WITH A REASON, NOT A MISSING NUMBER, and the
                        reason is on the cell. Comparing a part-county production figure against
                        the whole county's guarantee values the unmeasured acres at ZERO, which
                        is the largest shortfall a policy can show — the Rogers Farms inversion,
                        narrowed to one county. */}
                    <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                      fontWeight: ou.ok ? 800 : 400,
                      color: !ou.ok ? "#aab5aa" : ou.under ? "#a33" : "#2d6a2d" }}
                      title={ou.ok
                        ? Math.round(ou.production).toLocaleString() + " " + (ev.unit || "bu") + " (" + ou.basis + ") against a "
                          + Math.round(gBu).toLocaleString() + " " + (ev.unit || "bu") + " guarantee"
                        : ou.why}>
                      {ou.ok ? (ou.diff >= 0 ? "+" : "−") + Math.abs(Math.round(ou.diff)).toLocaleString() : "—"}
                    </td>
                  </>);
                })()}
                <td style={{ padding: "6px 0", fontWeight: 600,
                  color: c.status === "stated" ? "#2d6a2d" : c.status === "unsettled" ? "#a33" : "#8a6d1f" }}>
                  {c.status === "stated" ? "stated" : c.status === "unsettled" ? "UNSETTLED" : "evidence, not stated"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}


      {/* ⚠ AN UNKEYED COUNTY IS STATED PLAINLY RATHER THAN LEFT LOOKING LIKE A GAP. It used to
          read "UNSETTLED" in the county table and then block the report — Kevin, on Bridgewater:
          "it says we dont have enough data but I think we do for this customer". STEUBEN carries
          acres NULL, liability NULL and zero fields; there is nothing there to report. */}
      {ev.unkeyedCounties && ev.unkeyedCounties.length ? (
        <div style={{ fontSize: 11.5, color: "#5a6a5a", marginBottom: 8 }}>
          {ev.unkeyedCounties.join(", ")} {ev.unkeyedCounties.length === 1 ? "has a coverage row but no acreage keyed" : "have coverage rows but no acreage keyed"}
          {" "}for this crop year, so {ev.unkeyedCounties.length === 1 ? "it is" : "they are"} not counted as missing production.
        </div>
      ) : null}

      {/* ── WHAT IS IN THE WAY ── */}
      {ev.blockers.length ? (
        <div style={{ fontSize: 12.5, color: "#8a6d1f" }}>
          <div style={{ fontWeight: 800, marginBottom: 2 }}>Before this could be reported:</div>
          {ev.blockers.map((b, i) => <div key={i} style={{ marginTop: 2 }}>• {b}</div>)}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: "#2d6a2d", fontWeight: 700 }}>
          Nothing is in the way of reporting this — which is a statement about obstacles, not
          a statement that the figures are right. Check them against the grower's own records.
        </div>
      )}

      {/* ⚠ THIS LINE USED TO SAY "A filling step is not built yet: the Dropbox production
          folder has no Ready-for-Silas lane and the stager still refuses production forms, so
          this screen reviews and does not yet stamp." Every clause of that was true when it
          was written and all three became false on 2026-10-02, while it went on printing
          directly beneath the fill button — telling the reader the control above it does not
          exist. Found by RENDERING the panel, not by reading it: every checker was green.
          Third time this repo has recorded a sentence that quietly outlived its measurement
          (`stampBlocked` and the backtest's "pre-2026" being the others), so what survives
          here is only the part that cannot go stale — where the record types come from. */}
      <div style={{ fontSize: 11, color: "#60735f", marginTop: 10, lineHeight: 1.5 }}>
        Record types are RMA's own, read from {ev.recordTypeSource.document}, page {ev.recordTypeSource.page}.
      </div>
    </div>
      </details>
    </>
  );
}

// ⚠ THE CLAIMS PAGE'S ACTIVE TAB TEST, at module scope so it has one definition. A claim the
// carrier closed having paid nothing is terminal (Closed, or R&H's own word Released — see
// the Claims page for the measurement), Paid is terminal, and every other status — including
// one nobody has seen yet — is ACTIVE. A negative rule on purpose: a positive whitelist would
// drift toward hiding a claim.
const CLAIM_TERMINAL_NO_PAYMENT = Object.freeze(["Closed", "Released"]);
const isActiveStatus = (s) => s !== "Paid" && !CLAIM_TERMINAL_NO_PAYMENT.includes(s);

// ⚠ THE 578'S PREVENTED ACRES, BEFORE THE AIP HAS KEYED THEM (Kevin, 2026-10-19).
// For a farm with an OPEN prevent-plant claim, the stored arrival read of its 578s
// (sql/104 fsa578_lines.pp_summary) becomes the engine's PP-acre input where — and only
// where — the AIP has keyed no acreage for that crop and county. Every figure built from it
// is labelled PROVISIONAL_LABEL. Admin only. Returns null (nothing to substitute) or
// { ppSummary, openPpKeys, why } — a why when the 578 cannot be used, so the panel says so.
async function provisional578Inputs({ farmerId, cropYear, pols }) {
  const docs = await sbFetch("farmer_578s", {
    select: "id,county,is_nationwide,tax_year",
    filters: [`farmer_id=eq.${farmerId}`, `tax_year=eq.${cropYear}`], order: "id.asc",
  });
  if (!docs.length) return null;
  const { use } = FSA578LINES.choose578Docs(docs);
  let reads = [];
  try {
    reads = await sbFetch("fsa578_lines", {
      select: "farmer_578_id,tie_status,parser_version,pp_summary",
      filters: [`farmer_578_id=in.(${use.map((d) => d.id).join(",")})`],
    });
  } catch (e) {
    // sql/104 not applied: no stored read exists, so nothing is substituted.
    return { ppSummary: [], why: "the 578 read store is not set up yet" };
  }
  const byId = new Map((reads || []).map((r) => [r.farmer_578_id, r]));
  // ⚠ A PARTLY-READ 578 SET IS NOT USED. One unread county reads exactly like a county with
  // no prevented acres, so either every chosen 578 has a usable read or none is used.
  for (const d of use) {
    const r = byId.get(d.id);
    if (!r) return { ppSummary: [], why: "a 578 on file has not been read yet" };
    if (!FSA578LINES.storedReadUsable(r, { requireLines: false })) {
      return { ppSummary: [], why: r.tie_status === "untied"
        ? "a 578's read did not tie to its own printed totals" : "a 578's read is from an older reader" };
    }
  }
  const ids = pols.map((p) => p.id);
  const [inds, notices] = await Promise.all([
    sbFetch("indemnities", { select: "policy_id,crop,crop_year,status,claim_stages,check_date,claim_date",
      filters: [`policy_id=in.(${ids.join(",")})`, `crop_year=eq.${cropYear}`] }),
    sbFetch("m13_notice_of_loss", { select: NOTICE_SELECT_COLUMNS, filters: [`crop_year=eq.${cropYear}`] }),
  ]);
  const pnById = new Map(pols.map((p) => [p.id, p.policy_number]));
  const claims = (inds || []).map((i) => ({ policyNumber: pnById.get(i.policy_id) || "", cropYear: i.crop_year,
    crop: i.crop, status: i.status, stages: i.claim_stages, checkDate: i.check_date, claimDate: i.claim_date }));
  const outstanding = outstandingNotices({
    notices: notices || [],
    policies: pols.map((p) => ({ id: p.id, farmerId: p.farmer_id, policyNumber: p.policy_number, aip: p.aip, cropYear: p.crop_year })),
    claims, cropYear, isSyncFormat: isSyncFormatPolicyNumber,
    cropForCode: (code) => RMA_CROPS[code] || null,
    isActiveStatus,
  });
  return {
    ppSummary: use.flatMap((d) => byId.get(d.id).pp_summary || []),
    openPpKeys: openPpClaimKeys({ claims, outstanding, cropYear, isActiveStatus }),
    why: null,
  };
}

function AdjusterPanel({ farmer, cropYear, isMobile }) {
  const bu = (n) => (n == null || !isFinite(n)) ? "—" : Math.round(n).toLocaleString();
  const money = (n) => (n == null || !isFinite(n)) ? "—" : "$" + Math.round(n).toLocaleString();
  const [state, setState] = React.useState({ loading: true, err: null, byPolicy: [] });
  const [frozen, setFrozen] = React.useState({});
  const [freezing, setFreezing] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    setState({ loading: true, err: null, byPolicy: [] });
    (async () => {
      try {
        const pols = await sbFetch("policies", { order: "id.asc",
          select: "id,policy_number,crop_year,farmer_id,aip,state",
          filters: [`farmer_id=eq.${farmer.id}`, `crop_year=eq.${cropYear}`],
        });
        if (!pols.length) { if (alive) setState({ loading: false, err: null, byPolicy: [] }); return; }
        const ids = pols.map((p) => p.id);
        const inList = `(${ids.join(",")})`;
        const [units, cov, elig, prices] = await Promise.all([
          sbFetch("crop_coverage_units", { order: "id.asc",
            select: "id,policy_id,county,crop,type_code,practice_code,plan,acres,aph_yield,"
                  + "coverage_level,share,is_prevent_plant,liability,acreage_status",
            filters: [`policy_id=in.${inList}`, `crop_year=eq.${cropYear}`],
          }),
          sbFetch("crop_coverage", { order: "id.asc",
            select: "id,policy_id,county,crop,type_code,mpci_plan_code,insurance_options,price_election_pct,iif_key",
            filters: [`policy_id=in.${inList}`],
          }),
          sbFetch("pp_eligibility", { order: "id.asc",
            select: "id,policy_id,county,crop,type,max_acres,planted_acres,remaining_acres",
            filters: [`policy_id=in.${inList}`],
          }),
          // ⚠ NOT state-filtered. RMA publishes a price per state and this book spans five;
          // the index is keyed on (crop, type, practice) and DROPS a cell whose rows
          // disagree, so admitting every state makes a cross-state difference show up as
          // "not priced" rather than as whichever row happened to sort first.
          sbFetch("rma_prices", { order: "id.asc",
            select: "commodity_code,state_code,type_code,practice_code,projected_price",
            filters: [`crop_year=eq.${cropYear}`],
          }),
        ]);
        const priceIndex = buildPriceIndex(prices, (code) => RMA_CROPS[code]);
        const built = buildPpCells({ units, coverages: cov, eligibility: elig,
                                     policies: pols, priceIndex, cropYear, stateFipsOf: ppStateFips });
        // ⚠ THE WHOLE FARM AT ONCE (adjudicateFarm): a prevented crop may borrow the unused
        // eligibility of a crop written on ANOTHER of this farm's policies in the same county
        // — corn rolling to wheat, which is nearly always its own policy.
        let groups = [];
        for (const [pid, g] of built) {
          groups.push({ policy: { ...g.policy, id: pid }, cells: g.cells,
            // App.jsx owns the factor ladder; ppCells holds no copy of it.
            model: buildModel({ cells: g.cells, cropYear, state: g.policy.state,
              resolveFactor: (input) => resolvePpFactor(input) }) });
        }
        // The 578's prevented acres where the AIP has not keyed the crop, for an open PP
        // claim — admin only (isCurrentUserAdmin is FALSE in preview and the portal). Keyed
        // acres always win; nothing substituted here is ever frozen.
        let prov = null;
        if (isCurrentUserAdmin()) {
          try {
            const pin = await provisional578Inputs({ farmerId: farmer.id, cropYear, pols });
            if (pin && pin.openPpKeys) {
              const p = applyProvisional578Pp(groups, { units, ppSummary: pin.ppSummary, openPpKeys: pin.openPpKeys });
              groups = p.groups; prov = { held: p.held, keyedWins: p.keyedWins, used: p.provisional, why: null };
            } else if (pin && pin.why) prov = { held: [], keyedWins: [], used: [], why: pin.why };
          } catch (e) { prov = { held: [], keyedWins: [], used: [], why: "the 578 could not be read: " + String(e.message || e) }; }
        }
        const dets = adjudicateFarm(groups);
        const out = [];
        for (const g of groups) {
          if (!dets.has(g.policy.id)) continue;
          out.push({ policyId: g.policy.id, policy: g.policy, det: dets.get(g.policy.id), cells: g.cells });
        }
        if (alive) setState({ loading: false, err: null, byPolicy: out, prov });
      } catch (e) {
        if (alive) setState({ loading: false, err: String(e.message || e), byPolicy: [] });
      }
    })();
    return () => { alive = false; };
  }, [farmer.id, cropYear]);

  // ⚠ FREEZING IS WHAT MAKES THE CROSS-CHECK A CHECK. Kevin: "Our estimate should be
  // frozen, we should never really have to change our estimate." sql/074 puts a BEFORE
  // UPDATE trigger on claim_estimates that raises, so this writes once per (crop year,
  // coverage, stage, rule version) and a second opinion is a new rule version, never an
  // overwrite. The SERVER decides `payment_known_at_estimate` — a client could claim
  // anything, and the one column that makes the row worth having is the one saying
  // nothing had been paid when it was written.
  async function freeze(row, d) {
    const key = row.policyId + "|" + d.key;
    setFreezing(key);
    try {
      const body = {
        cropYear, policyId: row.policyId, crop: d.crop, county: d.county,
        // The coverage this estimate is for. api/_claim-estimate.js REQUIRES it — without
        // it a payment can never be matched back and the row is not worth writing.
        iifKey: d.iifKey,
        farmerId: farmer.id, policyNumber: row.policy.policy_number, aip: row.policy.aip,
        // Which rule produced it. Two rows from two rules must never pool into one
        // accuracy claim, so the version travels with the figure.
        ruleVersion: ADJUDICATION_VERSION,
        // "PP" = prevent plant, ANY tier code. The carrier writes PF or P2 (CY2026 is almost
        // all P2, on both carriers), so freezing "PF" alone missed nearly every payment and
        // stamped paid claims as forecasts. The server matches it on the shared set.
        stageCode: "PP",
        estimatedIndemnity: d.indemnity, estimatedAcres: d.ppAcres,
        inputs: {
          rule: "ppAdjuster",
          ppAcres: d.ppAcres, eligibleAcres: d.eligibleAcres,
          eligibilityBasis: d.eligibilityBasis,
          aph: d.aph, coverageLevel: d.coverageLevel, priceElectionPct: d.priceElectionPct,
          ppFactor: d.ppFactor, factorBasis: d.factorBasis,
          guaranteeQty: d.guaranteeQty, projectedPrice: d.projectedPrice, share: d.share,
          perAcre: d.perAcre, placedAcres: d.placedAcres, unplacedAcres: d.unplacedAcres,
          allocations: d.allocations,
          orderDependent: row.det.orderDependent,
        },
        note: row.det.orderDependent
          ? "Order-dependent: more than one crop on this policy drew on shared eligibility."
          : null,
      };
      const res = await adminApiFetch("/api/market?fn=claim-estimate&op=freeze", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const r = await apiJson(res, "the estimate service");
      setFrozen((f) => ({ ...f, [key]: r && r.error ? { err: r.error } : { ok: true, at: new Date().toISOString() } }));
    } catch (e) {
      setFrozen((f) => ({ ...f, [key]: { err: String(e.message || e) } }));
    } finally { setFreezing(null); }
  }

  const lbl2 = { fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: "#60735f", textTransform: "uppercase" };
  const box = { border: "1px solid #dce7dc", borderRadius: 9, padding: isMobile ? 10 : 13, marginTop: 10, background: "#fbfdfb" };

  if (state.loading) return <div style={{ ...box, color: "#60735f", fontSize: 13 }}>Working the claim…</div>;
  if (state.err) return <div style={{ ...box, color: "#8a3324", fontSize: 13 }}>Couldn't work the claim: {state.err}</div>;
  // What the 578 could not supply, said once, wherever it applies.
  const provNote = state.prov && (state.prov.why || (state.prov.held || []).length) ? (
    <div style={{ ...box, fontSize: 12, color: "#7a5a2d", background: "#fdf6e3", borderColor: "#e8dcb8" }}>
      {state.prov.why ? <div>The 578's prevented acres were not used: {state.prov.why}.</div> : null}
      {(state.prov.held || []).map((h, i) => (
        <div key={i}>The 578 reports {bu(h.acres)} ac prevented {h.crop}{h.county ? " in " + h.county : ""} that are not in any figure — {h.why}.</div>
      ))}
    </div>
  ) : null;
  if (!state.byPolicy.length) {
    return <>{provNote}<div style={{ ...box, color: "#60735f", fontSize: 13 }}>
      No prevent-plant acres are reported on this farm's CY{cropYear} policies, so there is
      nothing to adjudicate.
    </div></>;
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ ...lbl2, marginBottom: 6 }}>Our determination</div>
      {provNote}
      {state.byPolicy.map((row) => {
        const det = row.det;
        if (!det.available) {
          return <div key={row.policyId} style={box}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{row.policy.policy_number}</div>
            <div style={{ fontSize: 12, color: "#60735f", marginTop: 4 }}>{det.why}</div>
          </div>;
        }
        return (
          <div key={row.policyId} style={box}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#1a2e1a" }}>
                {row.policy.policy_number}
                <span style={{ color: "#60735f", fontWeight: 600, fontSize: 12 }}> · {row.policy.aip}</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#2d4a2d" }}>{money(det.indemnity)}</div>
            </div>
            {det.provisional && (
              <div style={{ marginTop: 8, padding: "7px 9px", background: "#fdf6e3", border: "1px solid #e8dcb8", borderRadius: 7, fontSize: 12, color: "#7a5a2d" }}>
                ⚠ {PROVISIONAL_LABEL}. The AIP has not keyed this crop's acres; the prevented acres
                below are read off the grower's 578. Once the AIP keys them, its figures replace these.
              </div>
            )}

            {/* ⚠ THE CAVEATS SIT ABOVE THE DETAIL, NOT UNDER IT. An order-dependent total or
                4,500 unpriced acres change how the figure should be read, and a reader who
                meets them after the number has already taken it. */}
            {det.orderDependent && (
              <div style={{ marginTop: 8, padding: "7px 9px", background: "#fdf6e3", border: "1px solid #e8dcb8", borderRadius: 7, fontSize: 12, color: "#7a5a2d" }}>
                ⚠ {det.orderNote}
              </div>
            )}
            {det.undeterminedAcres > 0.05 && (
              <div style={{ marginTop: 8, padding: "7px 9px", background: "#fdf3f0", border: "1px solid #eccfc6", borderRadius: 7, fontSize: 12, color: "#8a3324" }}>
                {bu(det.undeterminedAcres)} ac are NOT in this figure — we could not price them.
                {det.undetermined.map((u, i) => <div key={i} style={{ marginTop: 3 }}>· {u.crop} / {u.county} — {u.why}</div>)}
              </div>
            )}
            {det.unplacedAcres > 0.05 && (
              <div style={{ marginTop: 8, padding: "7px 9px", background: "#fdf3f0", border: "1px solid #eccfc6", borderRadius: 7, fontSize: 12, color: "#8a3324" }}>
                {bu(det.unplacedAcres)} ac could not be placed — no crop on this policy has
                eligible prevent-plant acres left (¶26C(9)). These acres are NOT payable.
              </div>
            )}
            {/* Held for a person, named on every determination — never modelled. */}
            {(det.notEvaluated || []).length > 0 && (
              <div style={{ marginTop: 8, fontSize: 11.5, color: "#60735f", lineHeight: 1.55 }}>
                <strong>Not evaluated:</strong> {det.notEvaluated.map((x) => x.what).join(" · ")}
                {det.notEvaluated.map((x, i) => <div key={i}>· {x.what} — {x.why}</div>)}
              </div>
            )}

            {det.determinations.filter((d) => d.available).map((d) => {
              const key = row.policyId + "|" + d.key;
              const fz = frozen[key];
              return (
                <div key={d.key} style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #dce7dc" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#2d4a2d" }}>
                      {d.crop} · {d.county} <span style={{ color: "#60735f", fontWeight: 600 }}>{bu(d.ppAcres)} ac prevented</span>
                      {d.provisional && <span style={{ color: "#a4700f", fontWeight: 700, fontSize: 11.5 }}> · {PROVISIONAL_LABEL}</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{money(d.indemnity)}</div>
                  </div>

                  {/* The working. Every figure the number was built from. */}
                  <div style={{ marginTop: 5, fontSize: 12, color: "#4a5a4a", lineHeight: 1.65 }}>
                    <div>
                      <strong>Eligible acres:</strong> {d.eligibleAcres == null ? "not established" : bu(d.eligibleAcres) + " ac"}
                      <span style={{ color: "#60735f" }}> — {d.eligibilityWhy}</span>
                    </div>
                    {d.guaranteeQty != null ? (
                      <div>
                        <strong>Per acre:</strong> {d.guaranteeQty} bu/ac guarantee × ${d.projectedPrice}
                        {d.priceElectionPct != null && d.priceElectionPct !== 1 ? ` × ${(d.priceElectionPct * 100).toFixed(0)}% price election` : ""}
                        {" "}= ${Number(d.perAcre).toFixed(2)}
                        {(d.unitRates || []).length <= 1 && d.aph != null && (
                          <span style={{ color: "#60735f" }}> ({d.aph} APH × {(d.coverageLevel * 100).toFixed(0)}% × {(d.ppFactor * 100).toFixed(1)}% PP factor)</span>
                        )}
                      </div>
                    ) : (
                      // Units with different APHs, levels or practices are priced one by one,
                      // the way the carrier prices them — never at one unit's figure for all.
                      <div>
                        <strong>Per acre, by unit</strong> ({(d.ppFactor * 100).toFixed(1)}% PP factor
                        {d.priceElectionPct != null && d.priceElectionPct !== 1 ? `, ${(d.priceElectionPct * 100).toFixed(0)}% price election` : ""}):
                        {(d.unitRates || []).map((u, i) => (
                          <div key={i} style={{ paddingLeft: 10 }}>
                            · {bu(u.acres)} ac · {u.aph} APH × {(u.coverageLevel * 100).toFixed(0)}% → {u.guaranteeQty} bu/ac
                            {" "}= {u.perAcre == null ? "not priced" : "$" + Number(u.perAcre).toFixed(2)}
                            {u.status === "E" ? " · verified eligible by the AIP (E)" : ""}
                          </div>
                        ))}
                      </div>
                    )}
                    {d.eVerifiedAcres > 0 && (
                      <div style={{ color: "#60735f" }}>
                        {bu(d.eVerifiedAcres)} ac are coded E — eligible prevent-plant acres the AIP verified on another unit — so they are not re-capped against this crop's worksheet.
                      </div>
                    )}
                    {d.uninsurablePpAcres > 0 && (
                      <div style={{ color: "#60735f" }}>
                        {bu(d.uninsurablePpAcres)} ac are coded prevented AND uninsurable — no guarantee on this crop line, so they are not in this figure.
                      </div>
                    )}
                    <div><strong>Share:</strong> {d.share}</div>
                    {(d.allocations || []).map((a, i) => (
                      <div key={i} style={{ color: a.own ? "#4a5a4a" : "#2d3a6a" }}>
                        {a.own ? "· own eligibility" : (a.otherPolicy ? `· borrowed from ${a.crop} on policy ${a.otherPolicy}` : `· borrowed from ${a.crop} (${a.unit})`)}
                        {" "}{bu(a.acres)} ac @ ${Number(a.perAcre).toFixed(2)}
                        {a.own ? "" : <span style={{ color: "#60735f" }}> — {a.why}</span>}
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 7, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {/* ⚠ REFUSED UP FRONT WHERE THE COVERAGE KEY IS MISSING, rather than
                        offered and then 400'd. `claim_estimates` is keyed on `iif_key`
                        because m13_loss_detail carries no policy_id — an estimate without
                        it could never be matched to the payment it is meant to precede, so
                        the button says why instead of failing on click. */}
                    {/* ⚠ A PROVISIONAL FIGURE IS NOT FROZEN. Its acres are the 578's, not the
                        AIP's, and a frozen estimate is compared to what the carrier pays — a
                        figure the keyed acres will replace is not that estimate. */}
                    <button disabled={freezing === key || (fz && fz.ok) || !d.iifKey || !!d.provisional}
                      title={d.provisional ? "Provisional — from the 578, not yet keyed. Freeze it once the AIP has keyed the acres." : !d.iifKey ? "This coverage carries no AIP in-force key, so a payment could never be matched back to the estimate." : ""}
                      onClick={() => freeze(row, d)}
                      style={{ border: "1px solid #cdddcd", background: fz && fz.ok ? "#eef3ee" : "#fff", borderRadius: 7, padding: "5px 11px", fontSize: 12, fontWeight: 700, cursor: (fz && fz.ok) || !d.iifKey ? "default" : "pointer", color: d.iifKey ? "#2d4a2d" : "#9aab9a" }}>
                      {fz && fz.ok ? "✓ Frozen" : freezing === key ? "Freezing…" : "Freeze this estimate"}
                    </button>
                    {fz && fz.err && <span style={{ fontSize: 11, color: "#8a3324" }}>{fz.err}</span>}
                    <span style={{ fontSize: 11, color: "#60735f" }}>
                      {d.iifKey
                        ? "Frozen before the carrier pays — written once, never updated."
                        : "Can't freeze: this coverage carries no AIP in-force key, so a payment could never be matched back to it."}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* ⚠ NAMED, NEVER OMITTED. A model handed a snapshot with a hole in it reaches
                for what it knows; so does a reader. */}
            {(det.absences || []).length > 0 && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: "pointer", fontSize: 11, color: "#60735f", fontWeight: 700 }}>
                  What this determination could not source ({det.absences.length})
                </summary>
                <div style={{ marginTop: 6, fontSize: 11, color: "#60735f", lineHeight: 1.6 }}>
                  {det.absences.map((a, i) => <div key={i}>· <strong>{a.what}</strong> — {a.why}</div>)}
                </div>
              </details>
            )}
          </div>
        );
      })}

      <AdjusterChat farm={farmer.name} cropYear={cropYear}
        determination={state.byPolicy.length === 1 ? state.byPolicy[0].det : null}
        policies={state.byPolicy} isMobile={isMobile} />
    </div>
  );
}

// ── ASKING IT ────────────────────────────────────────────────────────────────
//
// ⚠ THE CHAT ANSWERS ABOUT THE DETERMINATION ON SCREEN AND NOTHING ELSE. It is sent the
// determination object, not a farmer id, so it cannot reach a table and summarise one
// wrongly — and api/_adjuster-chat.js refuses any answer carrying a number that is not in
// what it was handed. A refusal is shown INSTEAD of the answer, never beside it: a warning
// under a confident sentence reads as a footnote, and this is a figure an agent repeats to
// a grower.
function AdjusterChat({ farm, cropYear, determination, policies, isMobile }) {
  const [msgs, setMsgs] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [pick, setPick] = React.useState(0);

  const det = determination || (policies && policies[pick] ? policies[pick].det : null);

  async function ask() {
    const question = q.trim();
    if (!question || busy || !det) return;
    setQ("");
    setMsgs((m) => [...m, { role: "user", content: question }]);
    setBusy(true);
    try {
      const res = await adminApiFetch("/api/market?fn=adjuster-chat", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
        determination: det, farm, cropYear, question,
        history: msgs.slice(-8).map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      const r = await apiJson(res, "the adjuster");
      if (r && r.answer) setMsgs((m) => [...m, { role: "assistant", content: r.answer, citations: r.citations || [] }]);
      else setMsgs((m) => [...m, { role: "assistant", refusal: (r && (r.refusal || r.error)) || "No answer." }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", refusal: String(e.message || e) }]);
    } finally { setBusy(false); }
  }

  if (!det) return null;
  return (
    <div style={{ marginTop: 14, border: "1px solid #dce7dc", borderRadius: 9, background: "#fff" }}>
      <div style={{ padding: "9px 13px", borderBottom: "1px solid #eef3ee", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#2d4a2d" }}>Ask the adjuster</div>
        {policies && policies.length > 1 && (
          <select value={pick} onChange={(e) => setPick(Number(e.target.value))}
            style={{ fontSize: 11, padding: "3px 6px", border: "1px solid #cdddcd", borderRadius: 6 }}>
            {policies.map((p, i) => <option key={p.policyId} value={i}>{p.policy.policy_number}</option>)}
          </select>
        )}
      </div>
      <div style={{ padding: 13 }}>
        {!msgs.length && (
          <div style={{ fontSize: 12, color: "#60735f", lineHeight: 1.6 }}>
            It answers only from this determination and the handbook paragraphs behind it,
            and cites the paragraph for any rule it states. It will not tell you what the
            AIP will pay.
            <div style={{ marginTop: 6 }}>
              {["Why did corn borrow acres from soybeans?",
                "What would change if the worksheet were loaded?",
                "How was the eligibility established here?"].map((s) => (
                <button key={s} onClick={() => setQ(s)}
                  style={{ display: "block", textAlign: "left", marginTop: 4, border: "1px solid #e4ece4", background: "#fbfdfb", borderRadius: 6, padding: "4px 8px", fontSize: 11, color: "#4a5a4a", cursor: "pointer" }}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{ marginBottom: 9 }}>
            {m.role === "user"
              ? <div style={{ fontSize: 12, fontWeight: 700, color: "#2d4a2d" }}>{m.content}</div>
              : m.refusal
                ? <div style={{ fontSize: 12, color: "#8a3324", background: "#fdf3f0", border: "1px solid #eccfc6", borderRadius: 7, padding: "7px 9px" }}>{m.refusal}</div>
                : <div style={{ fontSize: 12.5, color: "#3a4a3a", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                    {m.content}
                    {(m.citations || []).length > 0 && (
                      <div style={{ marginTop: 4, fontSize: 10.5, color: "#60735f" }}>
                        Cites {m.citations.join(", ")} — FCIC-25370
                      </div>
                    )}
                  </div>}
          </div>
        ))}
        <div style={{ display: "flex", gap: 7, marginTop: 8 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
            placeholder="Ask about this determination…"
            style={{ flex: 1, padding: "7px 10px", border: "1px solid #cdddcd", borderRadius: 7, fontSize: 12, fontFamily: "inherit" }} />
          <button onClick={ask} disabled={busy || !q.trim()}
            style={{ border: "none", background: busy ? "#cdddcd" : "#2d4a2d", color: "#fff", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: busy ? "default" : "pointer" }}>
            {busy ? "…" : "Ask"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ⚠ THE MODEL EMITS MARKDOWN AND THE FIRST CUT PRINTED IT RAW. Found by screenshotting the
// finished page, not by any checker: a correct, cited answer rendered as
// `**FCIC-25370 §83, p.82:**` and `> "In order for planted acres…"` — asterisks and angle
// brackets littered through the one screen built to be READ at length.
//
// The ground rules ask it to quote the handbook's own sentence, so it reaches for a
// blockquote; telling it "no markdown" is a request a model can ignore, and this text is
// the whole product. So it is RENDERED. Deliberately tiny — bold, blockquote, bullets and
// headings, nothing else — because a markdown library is a new dependency in a repo whose
// package.json is kept to four, and anything richer invites HTML into a model's output.
// No `dangerouslySetInnerHTML` anywhere: every branch returns React elements, so a stray
// `<script>` in an answer is text.
function askInline(text, keyBase) {
  // **bold** and *italic*. Split on the delimiter and alternate, so an unmatched marker
  // renders as the literal character rather than swallowing the rest of the paragraph.
  // ⚠ BOLD FIRST, THEN ITALIC INSIDE EACH SURVIVING SEGMENT — splitting on a single `*`
  // first would cut `**` in half and turn every bold run into two stray asterisks. The
  // italic pass is what the second screenshot caught: `*same crop*` was still printing
  // its markers because only `**` was handled.
  const ital = (s, k) => String(s).split(/(?<!\*)\*(?!\*)/).map((q, j) => (j % 2 === 1
    ? <em key={k + "i" + j}>{q}</em>
    : <React.Fragment key={k + "n" + j}>{q}</React.Fragment>));
  return String(text).split(/\*\*/).map((p, i) => (i % 2 === 1
    ? <strong key={keyBase + "b" + i}>{ital(p, keyBase + "b" + i)}</strong>
    : <React.Fragment key={keyBase + "t" + i}>{ital(p, keyBase + "t" + i)}</React.Fragment>));
}
function AskMarkdown({ text }) {
  const lines = String(text == null ? "" : text).split("\n");
  const out = [];
  let list = null;
  const flush = () => {
    if (list && list.length) out.push(<ul key={"u" + out.length} style={{ margin: "5px 0 5px 18px", padding: 0 }}>{list}</ul>);
    list = null;
  };
  lines.forEach((raw, i) => {
    const l = raw.trimEnd();
    if (/^\s*$/.test(l)) { flush(); return; }
    const bullet = l.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) { (list = list || []).push(<li key={"l" + i} style={{ marginBottom: 2 }}>{askInline(bullet[1], "l" + i)}</li>); return; }
    flush();
    const quote = l.match(/^\s*>\s?(.*)$/);
    if (quote) {
      out.push(<div key={"q" + i} style={{ borderLeft: "3px solid #cdddcd", paddingLeft: 10, margin: "5px 0", color: "#4a5a4a", fontStyle: "italic" }}>{askInline(quote[1], "q" + i)}</div>);
      return;
    }
    const head = l.match(/^\s*#{1,6}\s+(.*)$/);
    if (head) {
      out.push(<div key={"h" + i} style={{ fontWeight: 800, color: "#2d4a2d", margin: "9px 0 3px" }}>{askInline(head[1], "h" + i)}</div>);
      return;
    }
    out.push(<div key={"p" + i} style={{ marginBottom: 3 }}>{askInline(l, "p" + i)}</div>);
  });
  flush();
  return <div>{out}</div>;
}

// ── ASK THE ADJUSTER — a standalone handbook desk ────────────────────────────
//
// Kevin, 2026-09-29: *"It needs to be a standalone chat bot, not within a particular
// claim. I want this to be something we can ask questions that aren't tied to a specific
// claim, just questions that have come up."*
//
// `AdjusterChat` (inside a claim) answers about ONE determination and refuses everything
// else. This answers about the HANDBOOKS and reads no customer data at all — different
// question, different safety story, so it is a separate component rather than a mode flag
// on that one. See api/_adjuster-ask.js for the whole of that story.
//
// ⚠⚠ THE PASSAGES ARE NOT AN OPTIONAL EXTRA AND MUST NOT BE HIDDEN BEHIND A SETTING.
// The numeral guard can prove a figure came from the corpus; nothing mechanical can prove
// the model attached it to the right thing. The probe caught exactly that — "the PP
// coverage level factor (e.g. 60% or 65%)", where both numbers are genuinely in the
// retrieved text meaning an APH substitution and an acre count. **An agent reading the
// handbook's own sentence is the only backstop for that class**, so every answer carries
// its sources, one click from the text, and the empty state says to use them.
function AdjusterAskView({ isMobile }) {
  const [msgs, setMsgs] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [openSrc, setOpenSrc] = React.useState(null);   // index of the message whose sources are open
  const endRef = React.useRef(null);

  React.useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs, busy]);

  async function ask(text) {
    const question = (text == null ? q : text).trim();
    if (!question || busy) return;
    setQ("");
    setMsgs((m) => [...m, { role: "user", content: question }]);
    setBusy(true);
    try {
      const res = await adminApiFetch("/api/market?fn=adjuster-ask", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          history: msgs.slice(-8).map((m) => ({ role: m.role, content: m.content || m.refusal || "" })),
        }),
      });
      const r = await apiJson(res, "the adjuster");
      if (r && r.answer) {
        setMsgs((m) => [...m, { role: "assistant", content: r.answer,
          passages: r.passages || [], citations: r.citations || [], cited: !!r.cited, retrieval: r.retrieval || null }]);
      } else {
        setMsgs((m) => [...m, { role: "assistant", refusal: (r && (r.refusal || r.error)) || "No answer.",
          passages: (r && r.passages) || [], retrieval: (r && r.retrieval) || null }]);
      }
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", refusal: String(e.message || e) }]);
    } finally { setBusy(false); }
  }

  const SUGGEST = [
    "When can prevented planting acres be rolled to another crop?",
    "Do planted acres reduce prevented planting eligibility?",
    "What is the added land ratio and what proof does it need?",
    "Who may measure acres, and in what order of preference?",
    "What does paragraph 26C(9)(d) say about where rolled acres must be?",
  ];

  const pad = isMobile ? 16 : 32;
  return (
    <div style={{ padding: pad, maxWidth: 940 }}>
      <h2 style={{ fontFamily: "'Lora',serif", color: "#1a2e1a", margin: "0 0 4px" }}>Ask the Adjuster</h2>
      <div style={{ fontSize: 12.5, color: "#5d7a5d", margin: "0 0 14px", lineHeight: 1.6 }}>
        RMA loss-adjustment procedure, answered from the handbooks themselves and cited to
        the section and page. It reads no customer data — open a farm in Claims for that.
      </div>

      {/* ⚠ THE SCOPE IS STATED BEFORE THE FIRST QUESTION, NOT AFTER A BAD ANSWER. Two
          handbooks is not "crop insurance", and a reader who does not know the edge will
          read a decline as a broken tool rather than as an honest one. */}
      {/* ⚠ THIS LIST IS PINNED TO THE ACTUAL CORPUS BY `check-adjuster-ask`, which reads
          api/_handbook-index.js and fails if a form number here is missing from it or a
          loaded book is missing from here. A hand-kept list of what is loaded is exactly
          the thing that drifts, and drifting in the "says loaded when it is not" direction
          is a promise the page cannot keep. */}
      <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ flex: "1 1 320px", border: "1px solid #dce7dc", borderRadius: 9, background: "#fbfdfb", padding: "9px 12px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: "#2d4a2d", letterSpacing: .4, marginBottom: 4 }}>LOADED — 7 RMA HANDBOOKS + THE POLICY ITSELF</div>
          <div style={{ fontSize: 11.5, color: "#4a5a4a", lineHeight: 1.6 }}>
            Prevented Planting <span style={{ color: "#60735f" }}>(FCIC-25370, 2026)</span> ·
            Loss Adjustment Manual <span style={{ color: "#60735f" }}>(FCIC-25010, 2026)</span>
            <div style={{ marginTop: 3 }}>
              Per crop: Corn <span style={{ color: "#60735f" }}>(FCIC-25080, 2024)</span> ·
              Soybean <span style={{ color: "#60735f" }}>(FCIC-25440, 2026)</span> ·
              Grain Sorghum <span style={{ color: "#60735f" }}>(FCIC-25210, 2026)</span> ·
              Small Grains <span style={{ color: "#60735f" }}>(FCIC-25430, 2024)</span> ·
              Dry Bean <span style={{ color: "#60735f" }}>(FCIC-25110, 2022)</span>
            </div>
            {/* ⚠ THE SPREAD IS SHOWN, NOT AVERAGED AWAY, because the books are not all
                of one crop year — Dry Bean is effective from 2022 and the rest from 2024 or
                2026, and a reader who assumes everything here is this crop year would take
                an older rule as new.
                ⚠ THIS ONCE CLAIMED Small Grains was the 06-2015 edition and "still what RMA
                publishes". That was WRONG — RMA publishes FCIC-25430 (08-2023), effective
                for 2024 and succeeding crop years, and the corpus was three editions behind
                on the wheat handbook while this line asserted it was current. Measured
                against RMA's own file listing 2026-09-30 and replaced. */}
            {/* ⚠ THE REGULATION WAS ADDED 2026-09-30 BECAUSE THE SETTLEMENT ARITHMETIC WAS
                NOT QUOTABLE AT ALL. The LAM has no indemnity-calculation part — it ends at
                Part 12 plus exhibits — so the formula that decides what a grower is PAID,
                the appraisal minimums and the replant bushel caps live in the Crop
                Provisions, which are regulation rather than handbook.
                A REGULATION HAS NO PAGES, so those citations read "7 CFR 457.113 §12"
                with no page, rather than inventing one. */}
            <div style={{ marginTop: 5 }}>
              The policy: Basic Provisions <span style={{ color: "#60735f" }}>(7 CFR 457.8)</span> ·
              Coarse Grains — corn, sorghum, soybeans <span style={{ color: "#60735f" }}>(7 CFR 457.113, 2025)</span> ·
              Small Grains — wheat, barley, oats <span style={{ color: "#60735f" }}>(7 CFR 457.101, 2025)</span>
            </div>
            <div style={{ marginTop: 4, fontSize: 10.5, color: "#60735f" }}>
              The year is the crop year each is effective from — all are the current
              edition, and they span 2022 to 2026. Answers name the edition where it matters.
              The Basic Provisions are a <b>continuous policy</b> and carry no crop year, so
              they are cited by the eCFR snapshot they were taken from.
            </div>
          </div>
        </div>
        <div style={{ flex: "1 1 260px", border: "1px solid #eccfc6", borderRadius: 9, background: "#fdf9f7", padding: "9px 12px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: "#8a3324", letterSpacing: .4, marginBottom: 4 }}>NOT LOADED</div>
          <div style={{ fontSize: 11.5, color: "#7a5348", lineHeight: 1.6 }}>
            Livestock (LRP, DRP) · PRF · the <b>Special</b> Provisions — including
            every <b>Discount Factor chart</b> — and the loss handbooks for crops not listed.
            {/* ⚠ THE BASIC AND CROP PROVISIONS CAME OFF THIS LIST 2026-09-30 and are now in
                the loaded panel. Leaving them here would be the mirror of what this panel
                exists to prevent: telling a reader we cannot answer something we can.
                The SPECIAL Provisions stay, and the distinction is the one that matters —
                the charts are per crop, county AND year, so they are a lookup rather than
                a document; a retrieved passage would give a factor right for some county
                somewhere, which is worse than saying we do not hold it. */}
            <div style={{ marginTop: 4, fontSize: 10.5 }}>
              Discount factors, rates and coverage levels are set per crop, county and crop
              year in the Actuarial Documents. A worked example in a handbook is not one.
            </div>
          </div>
        </div>
      </div>
      <div style={{ border: "1px solid #dce7dc", borderRadius: 10, background: "#fff" }}>
        <div style={{ padding: isMobile ? 13 : 18, minHeight: 180 }}>
          {!msgs.length && (
            <div style={{ fontSize: 12.5, color: "#60735f", lineHeight: 1.7 }}>
              Every answer cites the section and page it came from, and carries the
              passages it was built on — <strong style={{ color: "#5d7a5d" }}>open them before
              you act on a figure.</strong> A number can be correctly quoted out of the
              handbook and still be a number about something else.
              <div style={{ marginTop: 10 }}>
                {SUGGEST.map((s) => (
                  <button key={s} onClick={() => ask(s)}
                    style={{ display: "block", textAlign: "left", marginTop: 5, border: "1px solid #e4ece4", background: "#fbfdfb", borderRadius: 7, padding: "6px 10px", fontSize: 11.5, color: "#4a5a4a", cursor: "pointer", width: "100%" }}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {msgs.map((m, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              {m.role === "user" ? (
                <div style={{ fontSize: 13, fontWeight: 700, color: "#2d4a2d", marginBottom: 2 }}>{m.content}</div>
              ) : (
                <div>
                  {m.refusal ? (
                    <div style={{ fontSize: 12.5, color: "#8a3324", background: "#fdf3f0", border: "1px solid #eccfc6", borderRadius: 7, padding: "8px 11px", lineHeight: 1.6 }}>{m.refusal}</div>
                  ) : (
                    <div style={{ fontSize: 13, color: "#3a4a3a", lineHeight: 1.7 }}><AskMarkdown text={m.content} /></div>
                  )}

                  {/* ⚠ THIN RETRIEVAL IS REPORTED, NOT HIDDEN. The score cannot separate an
                      in-scope question from an out-of-scope one (the distributions
                      interleave — see api/_adjuster-ask.js), so the reader is told when the
                      match was weak instead of the endpoint pretending to have filtered. */}
                  {m.retrieval && m.retrieval.thin && (
                    <div style={{ marginTop: 6, fontSize: 10.5, color: "#8a6a24", background: "#fdfaf0", border: "1px solid #ecdfc6", borderRadius: 6, padding: "5px 8px" }}>
                      Weak match — the passages found may be only loosely related to this question.
                    </div>
                  )}
                  {!m.refusal && !m.cited && (
                    <div style={{ marginTop: 6, fontSize: 10.5, color: "#8a6a24" }}>
                      This answer cites no section. Treat it as orientation, not as the rule.
                    </div>
                  )}

                  {(m.passages || []).length > 0 && (
                    <div style={{ marginTop: 7 }}>
                      <button onClick={() => setOpenSrc(openSrc === i ? null : i)}
                        style={{ border: "1px solid #e4ece4", background: "#fbfdfb", borderRadius: 6, padding: "4px 9px", fontSize: 11, color: "#4a5a4a", cursor: "pointer", fontWeight: 700 }}>
                        {openSrc === i ? "Hide" : "Show"} the {m.passages.length} passages this was drawn from
                        {m.citations && m.citations.length ? ` · cites ${m.citations.join(", ")}` : ""}
                      </button>
                      {openSrc === i && (
                        <div style={{ marginTop: 7, border: "1px solid #eef3ee", borderRadius: 8, background: "#fafcfa", padding: 10 }}>
                          {m.passages.map((p, k) => (
                            <div key={k} style={{ marginBottom: k === m.passages.length - 1 ? 0 : 11, paddingBottom: k === m.passages.length - 1 ? 0 : 11, borderBottom: k === m.passages.length - 1 ? "none" : "1px solid #eef3ee" }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: "#2d4a2d" }}>
                                {p.cite}{p.part ? <span style={{ fontWeight: 500, color: "#677167" }}> · chunk {p.part}</span> : null}
                              </div>
                              <div style={{ fontSize: 10.5, color: "#60735f", marginBottom: 4 }}>{p.title}</div>
                              <div style={{ fontSize: 11.5, color: "#4a5a4a", lineHeight: 1.6, whiteSpace: "pre-wrap", maxHeight: 260, overflowY: "auto", fontFamily: "ui-monospace,Menlo,Consolas,monospace" }}>{p.text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {busy && <div style={{ fontSize: 12, color: "#60735f" }}>Reading the handbooks…</div>}
          <div ref={endRef} />
        </div>

        <div style={{ display: "flex", gap: 8, padding: isMobile ? 13 : 18, paddingTop: 0 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
            placeholder="Ask about loss adjustment or prevented planting procedure…"
            style={{ flex: 1, padding: "9px 12px", border: "1px solid #cdddcd", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} />
          <button onClick={() => ask()} disabled={busy || !q.trim()}
            style={{ border: "none", background: busy || !q.trim() ? "#cdddcd" : "#2d4a2d", color: "#fff", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: busy || !q.trim() ? "default" : "pointer" }}>
            {busy ? "…" : "Ask"}
          </button>
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#677167", marginTop: 10, lineHeight: 1.6 }}>
        The handbooks are RMA procedure, not the policy. Where an answer turns on the
        Basic, Crop or Special Provisions, it says so rather than guessing what they say.
      </div>
    </div>
  );
}

function ClaimManagerView({ farmers, isMobile }) {
  // Sub-agents (portal role="agent", e.g. Daniele, Mock) get the Claim Manager scoped to
  // THEIR customers — the `farmers` prop IS their granted book (portalFarmers), and the roster
  // is built off farmerByDbId(farmers), so it shows exactly their farms (all 10 of Daniele's
  // grants, not just the 3 she writes) and nothing else. Admins (Kevin, Mike Meis) get the
  // whole book. No extra id filter — it once over-dropped grants on a farmer-id mismatch.
  const [year, setYear] = useState(CURRENT_CROP_YEAR);
  const [claimType, setClaimType] = useState(defaultClaimCampaign());
  // ⚠ THE CAMPAIGN BAR IS BUILT FROM THE BOOK, NOT FROM A TYPED LIST. `CLAIM_TYPES`
  // offered Prevent Plant plus corn, soybeans and wheat, so CY2026's other eleven
  // insured crops — potatoes ($7.5M of liability), grapes ($6.7M), onions, dry beans,
  // cabbage, processing beans, peaches, grain sorghum, apples, oats, triticale — had no
  // campaign at all. `coverage_snapshots` is the source because it carries crop, plan,
  // acres and liability for a whole crop year in ~1,300 rows, without needing the
  // policy-id round trip the main board query makes.
  const [bookCrops, setBookCrops] = useState([]);
  const campaigns = useMemo(() => claimCampaigns(bookCrops), [bookCrops]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [harvestMeta, setHarvestMeta] = useState(null);
  // ⚠ A FAILED READ IS NOT AN EMPTY BOOK. Every roster read used to `.catch(() => [])`, so a
  // failed `indemnities` read showed every farm with NO claim and flagged it, and a failed
  // `claim_tracking` read wiped every follow-up note off the screen. Now each failed read is
  // named, and the roster is NOT drawn from a partial read.
  const [loadErr, setLoadErr] = useState(null);        // ["table — reason", ...] or null
  const [reloadN, setReloadN] = useState(0);
  const [trackErr, setTrackErr] = useState(null);      // { name, msg } — a follow-up that did NOT save
  const [selected, setSelected] = useState(null);       // farmerId for the drill-in
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [facet, setFacet] = useState('all');   // has tickets / JD / claim / missing production
  const [flagOnly, setFlagOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');   // corn only: all | grain | silage (deadlines differ)
  // ⚠ THE LANE IS ADMIN-ONLY BECAUSE ITS SOURCE IS. `m13_notice_of_loss` is the
  // RAW AIP MIRROR and carries a single admin SELECT policy, so a scoped
  // sub-agent (portal role=agent — three active today) gets ZERO rows from it.
  // RLS holds, so this is not a leak; it is worse in the way that matters here,
  // because the lane would print a confident 0 chop appraisals against a real
  // 52. Showing 14 of 52 by falling back to the R&H-only claim column is no
  // better — a partial cohort with nothing on screen to say it is partial.
  // So the lane is HIDDEN rather than degraded, and check-portal carries the
  // baseline entry explaining why the read is safe. Widening the mirror to a
  // farmer-scoped policy is a real security decision and is not made here.
  const chopLaneVisible = isCurrentUserAdmin();

  // ⚠ FALLING BACK TO THE FIXED MAP IS LOAD-BEARING, NOT DEFENSIVE. `claimType` is
  // chosen before the book has loaded (and can be a campaign the selected crop year
  // does not carry), so a bare `campaigns[claimType]` is undefined on first paint and
  // on any year switch — and `ct.crop` one line down throws, taking the whole board out.
  const ct = campaigns[claimType] || CLAIM_TYPES[claimType] || campaigns[PP_CAMPAIGN_KEY];
  // The unit every production figure on this board is stated in, and whether this
  // campaign's crop settles on a revenue test at all. Both are per crop; see
  // src/claims/claimCrops.js for the measurement behind each.
  const campaignUnit = claimUnit(String(ct.crop || '').toUpperCase(), null);
  // ⚠ NULL WHEN THE BOOK HAS NOT LOADED, never a guessed 'RP'. A default here would
  // assert a revenue plan for a campaign nobody has read the coverage of yet.
  const campaignBasis = ct.basis || null;
  const crop = ct.crop;   // null for Prevent Plant

  // ⚠ THE AGENT FILTER IS WHAT MAKES THIS A CALL LIST RATHER THAN A REPORT.
  // Kevin, 2026-09-09: "claims manager can do exactly what I need: Tell me who
  // doesnt have a silage appraisal open. If we could just add an agent filter to
  // claims manager, that would be perfect." The 'NO silage appraisal' facet already
  // returns the corn growers without one; an agent works their own share of it.
  const [agentFilter, setAgentFilter] = useState('all');

  const farmerByDbId = useMemo(() => {
    const m = {};
    (farmers || []).forEach(f => { (f.allFarmerDbIds?.length ? f.allFarmerDbIds : [f.id]).forEach(id => { if (id) m[id] = f; }); });
    return m;
  }, [farmers]);

  // Does an `indemnities` row match this campaign (crop for harvest, PP/PF stage for PP)?
  // Read straight from the DB (not in-memory farmer.claims, which lags freshly-synced
  // claims). Status is NOT filtered here — a Paid/settled claim must carry over too so it
  // shows as settled and clears the flag. Crop-year is scoped by the indemnities query.
  // ── WHO IS CHOPPING — read the NOTICES, not the claim row ───────────────────
  // `CS` is RMA's Chop/silage intention (ICE D00067) off the P58 Notice of Loss.
  // The first cut of this lane read `indemnities.insured_intention_codes`, which
  // is written by m13-sync and BY NOTHING ELSE — nau-sync has no such line at all.
  // So the board showed 14 R&H farms and NONE of NAU's, which Kevin caught on
  // sight: "missing all NAU silage appraisals".
  //
  // Measured 2026-09-01 on the live book, CY2026 corn notices carrying CS:
  //   NAU              66 notices / 40 policies / 38 farms   (filed 08-21..08-31)
  //   Rain & Hail      26 notices / 14 policies / 14 farms   (filed 08-26..08-31)
  // The board was showing about a quarter of the real cohort.
  //
  // `m13_notice_of_loss` is the RAW P58 mirror and carries the field for BOTH
  // carriers, so it is the source now. Two properties make it strictly better
  // than the claim row for this question:
  //   • it covers NAU, which the claim column never will until nau-sync writes it;
  //   • it does NOT decay. A claim set to `Released` loses every P58-derived
  //     field — 0 of 47 CY2025 and 0 of 14 CY2026 Released claims carry one — so
  //     a cohort read off the claim row silently shrinks as claims settle. The
  //     notice row stays.
  //
  // The two sources are UNIONED rather than swapped. This is a list of farms to
  // go and work, so a false negative (a chopping farm missing) costs far more
  // than a false positive, and neither source's absence can hide a farm.
  //
  // ⚠ THE JOIN KEY IS SYNC-FORMAT ONLY, and that is a correctness rule rather
  // than tidiness. The mirror stores the bare policy number — NAU `1024224`
  // against the book's `NY-944-1024224-26`, R&H `0805227` against `MP-0805227` —
  // so the book side has to be reduced to its core. Applied to the hand-loaded
  // carriers that reduction MIS-PARSES: Agri Sompo's `2026-NY-601-838587` and
  // ProAg's `36-415-1050573` both collapse to their AGENCY number, which
  // collides across 8 policies / 6 farms. Those carriers have no M13 file and so
  // can never have a notice row — giving them no key is both safe and honest.
  // Measured: 480 sync-format CY2026 policies, ZERO colliding keys.
  const chopNoticeKey = (policyNumber, aip) => {
    const pn = String(policyNumber || "").trim();
    if (!isSyncFormatPolicyNumber(pn)) return null;   // hand-loaded — no file, no notice
    const parts = pn.split("-");
    // NAU  ST-AGENCY-POLICY-YY → the third segment; R&H  MP-POLICY → the second.
    const core = parts.length >= 4 ? parts[2] : parts[1];
    const bare = String(core || "").replace(/^0+/, "");
    if (!bare) return null;
    return `${String(aip || "").toUpperCase()}|${bare}`;
  };
  // The same key from the mirror side, where the number is already bare.
  const chopNoticeKeyFromMirror = (row) =>
    `${String(row && row.aip || "").toUpperCase()}|${String(row && row.policy_number || "").trim().replace(/^0+/, "")}`;
  
  // farmer id → true, for farms with a CS notice on THIS campaign's commodity.
  // Commodity is matched, never widened: a chop notice on soybeans must not put a
  // farm on the corn board.
  const buildChopNoticeMap = (notices, pols, polFarmer, commodityCode) => {
    const byKey = {};
    (pols || []).forEach(p => {
      const k = chopNoticeKey(p.policy_number, p.aip);
      if (k) (byKey[k] = byKey[k] || []).push(p.id);
    });
    const out = {};
    (notices || []).forEach(n => {
      if (!String(n.insured_intention_codes || "").toUpperCase().split(/[^A-Z]+/).includes("CS")) return;
      if (commodityCode && String(n.commodity_code || "").replace(/^0+/, "") !== String(commodityCode).replace(/^0+/, "")) return;
      (byKey[chopNoticeKeyFromMirror(n)] || []).forEach(pid => {
        const f = polFarmer[pid];
        if (f) out[f] = true;
      });
    });
    return out;
  };

  // ── WHAT A CLAIM SETTLED ON IS WHAT THE MONEY SAYS ────────────────────────
  // `claim_stages` is the UNION of the stage records: NAU's P20X (the ADJUSTER'S
  // NOTICE, which carries no dollars) and P21/P26 (which carry the payments).
  // When those disagree the money decides — the same rule the crop/county cell
  // has followed since 2026-08-16, applied to the STAGE question it was never
  // extended to.
  //
  // Measured CY2026 corn (2026-09-01): ALL EIGHT paid claims whose stages name a
  // harvest stage settled entirely on P2 (prevent plant) or R (replant) — ZERO
  // harvest acres between them. Anchor Farms' $37,063 is 118.6 PP acres; Rauberdale's
  // $141,476 is 446.8. Every one showed '✓ Paid' on the Corn Harvest board and
  // CLEARED that farm's needs-claim flag, so a farm with no harvest claim at all
  // read as settled. CY2025 discriminates rather than empties: 82 real harvest
  // claims kept, 64 excluded.
  //
  // The excluded set is not only prevent plant. A claim whose stages read 'FL,H'
  // and whose money is entirely FL is an ECO/SCO AREA-PLAN payout, not a harvest
  // loss — Danielewicz, Du Mond, Austic and W Cleveland all carry one beside a
  // real harvest claim. Mulligan Family Farm's CY2025 $54,448 is FL-only under
  // 'HAR,PP' stages, so it is neither a harvest claim nor a PP one and now
  // appears on neither board.
  //
  // ⚠ A CLAIM WITH NO LINES OF ITS OWN HAS NOTHING TO CHECK AGAINST and falls
  // back to its stated stages. That is not a loophole, it is the open-claim case:
  // all 47 open CY2026 corn claims — the whole chop cohort among them — have no
  // lines yet because nothing has been adjusted. Without the fallback the board
  // would empty of exactly the claims it exists to work.
  //
  // ⚠ AND THE INDEX IS KEYED BY CLAIM NUMBER, NEVER BY POLICY. `loss_key_base`
  // holds the claim number and one policy routinely carries several claims:
  // Dunham's open chop claim 26093471 has no lines while the SAME POLICY's paid
  // PP claim 26058697 has plenty. A policy-level check reads the PP claim's P2
  // lines and drops the chop claim off the board — the defect this fix exists to
  // remove, re-created by a sloppier join.
  const HARVEST_LINE_STAGES = new Set(['H', 'UH', 'HAR']);
  // indemnity_lines rows → { any, harvest, pp } sets of CLAIM NUMBERS.
  const buildLineIndex = (lines) => {
    const any = new Set(), harvest = new Set(), pp = new Set();
    (lines || []).forEach(l => {
      const k = String(l.loss_key_base || '').trim();
      if (!k) return;                       // unattributable — cannot judge a claim
      any.add(k);
      const st = String(l.stage_code || '').toUpperCase();
      if (HARVEST_LINE_STAGES.has(st)) harvest.add(k);
      if (isPpStage(st)) pp.add(k);
    });
    return { any, harvest, pp };
  };

  const indMatchesCampaign = (ind, lineIdx) => {
    const stages = String(ind.claim_stages || '').toUpperCase();
    const isPP = stages.split(',').some(isPpStage);                  // Prevent Plant stage (shared rule)
    const isHarvest = /(^|,)(HAR|H|UH)(,|$)/.test(stages);           // Harvest-loss stage
    // The money governs on BOTH boards — one rule, so a claim cannot be refused
    // by one campaign and accepted by the other on different evidence.
    const claimKey = String(ind.claim_number || '').trim();
    const settled = lineIdx && claimKey && lineIdx.any.has(claimKey) ? lineIdx : null;
    if (ct.kind === 'pp') return settled ? settled.pp.has(claimKey) : isPP;
    const cc = String(ind.crop || '').toUpperCase();
    const want = (crop || '').toUpperCase();
    // A claim can cover SEVERAL crops (comma-joined, e.g. "CORN, SOYBEANS"), and fall small
    // grains are filed together — Michael J Meisenzahl's wheat + triticale are ONE harvest
    // claim stored as TRITICALE, so the wheat row would flag despite a real open claim. Match
    // if ANY of the claim's crops is what we want; the wheat campaign accepts ANY fall small
    // grain (FALL_CROPS = wheat/winter wheat/triticale/barley/rye).
    const claimCrops = cc.split(',').map(s => s.trim()).filter(Boolean);
    const wanted = want === 'WHEAT' ? FALL_CROPS : [want];
    const cropMatch = claimCrops.some(c => wanted.includes(c));
    if (!cropMatch) return false;
    // Harvest campaign must carry an actual HARVEST-LOSS stage (H / HAR / UH). Prevent Plant
    // (PP/PF), Replant (R), and area-plan (FL = ECO/SCO) claims are NOT harvest claims and must
    // not surface here — a corn replant or PP claim is not a corn-harvest claim. A mixed claim
    // with a harvest component (e.g. FL,H or H,UH) still counts. A claim with NO stage detail at
    // all is assumed harvest (legacy claims we ingested before stage parsing).
    // Where there is money to read, it is the answer. Where there is not — an open
    // claim, nothing adjusted yet — the stated stages are the best evidence there is.
    if (settled) return settled.harvest.has(claimKey);
    if (!stages) return true;
    return isHarvest;
  };
  // policy_id → farmer → representative matching claim. Prefer an ACTIVE (non-Paid) claim
  // for the badge when both exist; otherwise the Paid one. Either way its presence clears
  // the 🚩 (a claim's already open or settled — nothing to flag).
  const buildClaimMap = (inds, polFarmer, lineIdx) => {
    const m = {};
    (inds || []).forEach(ind => {
      if (!indMatchesCampaign(ind, lineIdx)) return;
      const f = farmerByDbId[polFarmer[ind.policy_id]];
      if (!f) return;
      const prev = m[f.id];
      if (!prev) { m[f.id] = ind; return; }
      // active outranks paid for display (still-in-progress is the current state)
      if (String(prev.status) === 'Paid' && String(ind.status) !== 'Paid') m[f.id] = ind;
    });
    return m;
  };
  // Human judgment overrides the measured (ticket) signal. The 🚩 means "under guarantee
  // and nobody's handled it." It clears when: a claim is already open, YOU reported them
  // over (they had grain in the bin / delivered elsewhere so tickets understated), or you
  // set a resolved status. It's forced ON when you explicitly mark "Under — Needs Claim".
  const RESOLVED_STATUSES = ['Over — No Claim', 'Claim Filed', 'Paid / Closed'];
  const rowFlag = (r) => {
    if (r.claim) return false;                                // a claim already exists (open OR settled)
    const status = r.tracking?.workflow_status;
    if (status === 'Under — Needs Claim') return true;        // you marked it a claim
    if (RESOLVED_STATUSES.includes(status)) return false;     // Over–No Claim / Filed / Paid clears it
    return r.kind === 'harvest' && r.calc?.measured === 'under';   // tickets say under, unresolved
  };
  const rowHandled = (r) => !rowFlag(r) && (r.claim || RESOLVED_STATUSES.includes(r.tracking?.workflow_status));

  // Records a failed roster read by table and stands in an empty list so Promise.all can
  // finish; the loader then REFUSES to draw the roster from what is left.
  const rosterReadFailed = (fails, table) => (e) => { fails.push(table + " — " + String((e && e.message) || e)); return []; };
  async function loadHarvest() {
    const tokens = (CC_CROP_TOKENS[crop] || [crop.toUpperCase()]).map(t => `"${t}"`).join(',');
    const cfg = HARVEST_DISCOVERY[crop.toUpperCase()];
    const sym = harvestContractSym(crop, year);
    // Coverage source: current crop year comes from the live crop_coverage; PRIOR years
    // come from coverage_snapshots (the frozen book for that year, incl. 2025). Snapshots
    // only go back to 2025 — earlier years have no coverage to claim-check against.
    const useSnap = Number(year) !== Number(CURRENT_CROP_YEAR);
    const isCornCampaign = String(crop || '').toUpperCase() === 'CORN';
    // The crops whose grain combine figure counts as production (claimAccounting.grainCombineOf):
    // corn, and soybeans since Kevin's 2026-11-03 "yes soybean claim manager should be using JD
    // Soybean yield data". These read the field-coverage columns so the board builds the drill-in's figure.
    const countsCombine = COMBINE_COUNTED_CROPS.includes(String(crop || '').toUpperCase());
    const fails = [];
    const [pols, ccAll, tks, inputsAll, trackAll, bars, inds, indLines, notices, jdRows, snapAll, apprAll] = await Promise.all([
      // ⚠ policy_number AND aip ARE READ BY buildChopNoticeMap, so they are SELECTED
      // here. Without them chopNoticeKey() got undefined for every policy, returned
      // null every time, and the notice map was EMPTY — so the chop lane fell back
      // entirely to the R&H-only claim column and printed 15 farms against a real 58.
      // check-query-contract cannot see this: the read happens inside a helper that
      // takes `pols` as a parameter, one call away from the query.
      sbFetch('policies', { select: 'id,farmer_id,crop_year,policy_number,aip' }).catch(rosterReadFailed(fails, 'policies')),
      sbFetch('crop_coverage', { select: 'policy_id,county,crop,type,type_code,practice_code,plan,aph_yield,coverage_level,acres,pp_acres,price_election,liability,price_election_pct', filters: [`crop=in.(${tokens})`] }).catch(rosterReadFailed(fails, 'crop_coverage')),
      sbFetch('grain_delivery_tickets', { filters: [`crop=eq.${crop}`, `crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'grain_delivery_tickets')),
      sbFetch('claim_check_inputs', { filters: [`crop=eq.${crop}`, `crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'claim_check_inputs')),
      sbFetch('claim_tracking', { filters: [`crop_year=eq.${year}`, `claim_type=eq.${claimType}`] }).catch(rosterReadFailed(fails, 'claim_tracking')),
      (cfg && sym) ? sbFetch('futures_contract_history', { select: 'bar_date,close', filters: [`commodity=eq.${cfg.commodity}`, `contract_sym=eq.${sym}`] }).catch(rosterReadFailed(fails, 'futures_contract_history')) : Promise.resolve([]),
      sbFetch('indemnities', { select: 'policy_id,crop,crop_year,status,claim_stages,claim_number,insured_intention_codes', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'indemnities')),
      // The paid lines. Only the three columns the stage rule reads — a claim
      // number, its stage, and the year — so the query supplies exactly what
      // the rule consumes and nothing more.
      sbFetch('indemnity_lines', { select: 'loss_key_base,stage_code,crop_year', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'indemnity_lines')),
      // The raw P58 notices. `insured_intention_codes` is the only field read,
      // and it is the one nau-sync never writes onto a claim row.
      sbFetch('m13_notice_of_loss', { select: 'aip,policy_number,commodity_code,insured_intention_codes,notice_date', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'm13_notice_of_loss')),
      // jd_operation_id rides along for the evidence rule below (gone / duplicate).
      // ⚠ CORN READS ITS CHOPPER PASSES TOO, and the columns buildFieldCoverage needs
      // (NEEDS_COLUMNS.jd_field_ftf): the board's grain combine figure is built from the same
      // field coverage as the drill-in's, and a field's combine-vs-chopper form decides whether
      // its bushels are grain at all. The machine CHECK below still reads the exact JD crop only.
      JD_CROP[crop] ? sbFetch('jd_field_ftf', countsCombine
        ? { select: 'farmer_id,county,acres,mean_value,operation_date,jd_operation_id,crop,metric,clu_id,jd_field_id', filters: [isCornCampaign ? `crop=in.("CORN","CORN SILAGE")` : `crop=eq.${JD_CROP[crop]}`, `metric=eq.yield`, `operation_date=gte.${year}-01-01`, `operation_date=lte.${year}-12-31`] }
        : { select: 'farmer_id,county,acres,mean_value,operation_date,jd_operation_id', filters: [`crop=eq.${JD_CROP[crop]}`, `metric=eq.yield`, `operation_date=gte.${year}-01-01`, `operation_date=lte.${year}-12-31`] }).catch(rosterReadFailed(fails, 'jd_field_ftf')) : Promise.resolve([]),
      useSnap ? sbFetch('coverage_snapshots', { select: 'farmer_id,county,crop,type,plan,aph_yield,coverage_level,acres,price_election,liability', filters: [`crop_year=eq.${year}`, `crop=in.(${tokens})`] }).catch(rosterReadFailed(fails, 'coverage_snapshots')) : Promise.resolve([]),
      // ⚠ APPRAISED PRODUCTION FOR THE WHOLE CAMPAIGN. Kevin, 2026-09-30: "I thought we
      // were adding an appraisal column to the claim manager dashboard." Until now
      // `silage_appraisals` was read only in the per-farm drill-in, so the board could
      // show a "Silage appraisal" BADGE and no number — and on LANG FARMS that badge sat
      // beside "no tickets · —", i.e. a row with no production evidence on screen while
      // we held 4,913 appraised bushels.
      //
      // `field_name` is selected because it carries the line's own FSN-TRACT-FIELD and the
      // stored `fsn` was stamped from the sheet header (see fsnTractField). Not used for
      // the board total, but selecting it keeps this query honest about what identifies a
      // line — the query-contract rule.
      String(crop || '').toUpperCase() === 'CORN'
        ? sbFetch('silage_appraisals', { select: 'farmer_id,crop,crop_year,county,appraised_acres,appraised_qty,appraised_bu,yield_unit,type_code,field_name,fsn,tract,field_no,unit_number', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'silage_appraisals'))
        : Promise.resolve([]),
    ]);
    if (fails.length) { setLoadErr(fails); setRows([]); return; }
    // Full policy→farmer map (all years) so cross-year-matched claims resolve; yearPids
    // still gates coverage to this crop year.
    const polFarmer = {}; const yearPids = new Set();
    pols.forEach(p => { polFarmer[p.id] = p.farmer_id; if (Number(p.crop_year) === Number(year)) yearPids.add(p.id); });
    // Farms whose P58 notice says CHOP, for this campaign's commodity. Corn is
    // RMA commodity 0041; a campaign whose crop has no code gets no map rather
    // than an unfiltered one, so a lane can never fill with another crop.
    const chopByFarmer = buildChopNoticeMap(notices, pols, polFarmer,
      (RMA_ADM_CROPS[String(crop || '').toUpperCase()] || {}).code || null);
    const claimByFarmer = buildClaimMap(inds, polFarmer, buildLineIndex(indLines));
    // John Deere yield-monitor production, aggregated per farmer × county (acre-weighted:
    // production = Σ mean_yield × acres). Source = jd_field_ftf, which is attributed by CLU
    // LAND OWNERSHIP (api/_jd.js), NOT the combine operator — so a custom-harvester's yield
    // credits the insured who OWNS each field, not whoever ran the machine. A reference
    // cross-check on delivered tickets; captures ALL harvested acres incl. binned/fed grain.
    // APPRAISED production per farmer. Bushels ONLY — an appraisal sheet can carry tons
    // (silage-coded fields) or pounds (popcorn), and a book that adds a tonnage to a
    // bushel total has said nothing. Non-bushel lines are counted separately so the
    // column can say they exist rather than dropping them.
    // ⚠ ONE ROLL-UP RULE: summarizeAppraisals, the same one the drill-in uses, so the
    // board and the drill-in cannot disagree about which lines are bushels. Every stored row
    // counts once — a re-issued worksheet REPLACES its predecessor at upload (#450), so a
    // superseded sheet's rows are no longer in the table to be counted twice.
    const apprRowsByFarmer = {};
    (apprAll || []).forEach(row => {
      const f = farmerByDbId[row.farmer_id]; if (!f) return;
      (apprRowsByFarmer[f.id] = apprRowsByFarmer[f.id] || []).push(row);
    });
    const apprByFarmer = {};
    Object.entries(apprRowsByFarmer).forEach(([fid, rowsF]) => {
      const sum = summarizeAppraisals(rowsF);
      // A bushel line on SILAGE-insured acres is not in the grain total (appraisalInsuredType),
      // so it counts with the lines that are named and not added.
      const buLines = rowsF.filter(r => String(r.yield_unit || '').toUpperCase() === 'BU'
        && appraisalInsuredType(r) !== 'silage'
        && Number.isFinite(Number(r.appraised_qty != null ? r.appraised_qty : r.appraised_bu))).length;
      apprByFarmer[fid] = { ...sum, acres: sum.buAcres, lines: rowsF.length, otherUnitLines: rowsF.length - buLines };
    });
    const jdByFarmer = {};
    // ⚠ ONLY EVIDENCE-GRADE JOHN DEERE ROWS (src/claims/jdEvidence.js) — a pass Deere no
    // longer returns, or one recorded twice, is not production (sandbox passes still count).
    const jdEvAll = (await jdEvidenceClient(jdRows || [])).rows;
    // The machine check is the exact JD crop (corn's chopper rows are read above for the
    // field coverage only — a chopper's dry-matter tons are never a bushel check).
    const jdEvRows = isCornCampaign ? jdEvAll.filter(row => row.crop === JD_CROP[crop]) : jdEvAll;
    jdEvRows.forEach(row => {
      const f = farmerByDbId[row.farmer_id]; if (!f) return;
      const prod = (Number(row.mean_value) || 0) * (Number(row.acres) || 0);
      const ac = Number(row.acres) || 0;
      const j = jdByFarmer[f.id] || (jdByFarmer[f.id] = { totalProd: 0, totalAcres: 0, byCounty: {} });
      j.totalProd += prod; j.totalAcres += ac;
      const c = j.byCounty[row.county] || (j.byCounty[row.county] = { prod: 0, acres: 0 });
      c.prod += prod; c.acres += ac;
    });
    const covByFarmer = new Map();
    if (useSnap) {
      // Prior year: coverage_snapshots rows carry farmer_id directly (no policies join).
      (snapAll || []).forEach(r => {
        const f = farmerByDbId[r.farmer_id];
        if (!f) return;
        if (!covByFarmer.has(f.id)) covByFarmer.set(f.id, { farmer: f, rows: [] });
        covByFarmer.get(f.id).rows.push(r);
      });
    } else {
      ccAll.forEach(r => {
        if (!yearPids.has(r.policy_id)) return;
        const f = farmerByDbId[polFarmer[r.policy_id]];
        if (!f) return;
        if (!covByFarmer.has(f.id)) covByFarmer.set(f.id, { farmer: f, rows: [] });
        covByFarmer.get(f.id).rows.push(r);
      });
    }
    const tkByFarmer = {}; tks.forEach(t => { const f = farmerByDbId[t.farmer_id]; if (f) (tkByFarmer[f.id] = tkByFarmer[f.id] || []).push(t); });
    const inpByFarmer = {}; inputsAll.forEach(i => { const f = farmerByDbId[i.farmer_id]; if (f) inpByFarmer[f.id] = i; });
    const trByFarmer = {}; trackAll.forEach(tr => { const f = farmerByDbId[tr.farmer_id]; if (f) trByFarmer[f.id] = tr; });
    const harvest = resolveHarvestPrice(crop, year, bars);
    setHarvestMeta(harvest);
    const out = [];
    // Planted/PP split per unit, current year only (coverage_snapshots carry no policy id).
    // A failed unit read is named like every other roster read: without the units the
    // planted split silently falls back to the PP-factor model, a different number.
    const unitFails = [];
    const claimUnits = useSnap ? [] : await sbFetch('crop_coverage_units', { select: CLAIM_UNITS_SELECT, filters: [`crop_year=eq.${year}`, `crop=in.(${tokens})`] }).catch(rosterReadFailed(unitFails, 'crop_coverage_units'));
    if (unitFails.length) { setLoadErr(unitFails); setRows([]); return; }
    // ── GRAIN COMBINE BUSHELS, THE DRILL-IN'S OWN FIGURE ────────────────────────
    // Kevin, 2026-11-03: "Combine is grain, choppers harvest silage as tons. So yes, if a field
    // is insured as grain and we have JD yield data for grain bushels, use that." The drill-in
    // counts grainCombineOf(buildFieldCoverage(...)) — the Claims page's "Combined" line — as
    // production; the board must count the SAME figure from the SAME inputs, or a row reads one
    // verdict and its drill-in another. Each read below mirrors ProductionEvidencePanel's
    // (same columns, same failure handling: policy_fields, operations and HMC marks degrade to
    // none there, so they degrade to none here), scoped book-wide to this crop year.
    const combineByFarmer = {};
    if (countsCombine) {
      // A refused read stops the board like every other roster read: a combine figure built
      // without its fields or passes would be a different number from the drill-in's.
      const cFails = [];
      const [cPf, cOps, cHmc] = await Promise.all([
        sbFetch('policy_fields', { select: 'policy_id,clu_id,crop,crop_year,fsn,tract,field_id,acres,pp_acres,county,insured_county,unit_number,sub_unit,type_code',
          filters: [`crop_year=eq.${year}`, isCornCampaign ? 'crop=ilike.*CORN*' : `crop=ilike.*${String(JD_CROP[crop] || crop).slice(0, 4).toUpperCase()}*`] }).catch(rosterReadFailed(cFails, 'policy_fields')),
        sbFetch('jd_field_operations', { select: 'farmer_id,jd_operation_id,jd_field_id,field_name,crop,operation_start,area_acres,dry_yield,yield_unit,moisture_pct,source_present,is_duplicate,source_host',
          filters: ['operation_type=eq.harvest', `operation_start=gte.${year}-01-01`, `operation_start=lt.${Number(year) + 1}-01-01`] }).catch(rosterReadFailed(cFails, 'jd_field_operations')),
        isCurrentUserAdmin()
          ? sbFetch('claim_hmc_fields', { select: 'farmer_id,field_key,set_by_label,set_at',
              filters: [`crop_year=eq.${year}`, `crop=eq.${encodeURIComponent(String(crop).toUpperCase())}`] }).catch(rosterReadFailed(cFails, 'claim_hmc_fields'))
          : Promise.resolve([]),
      ]);
      if (cFails.length) { setLoadErr(cFails); setRows([]); return; }
      // The drill-in reads ONE farmer id (the panel's `farmer.id`), so this does too.
      const byDbId = (list, keyOf) => { const m = {}; (list || []).forEach(r => { const k = keyOf(r); if (k) (m[k] = m[k] || []).push(r); }); return m; };
      const pfBy = byDbId(cPf, r => yearPids.has(r.policy_id) ? polFarmer[r.policy_id] : null);
      const ftfBy = byDbId(jdEvAll, r => r.farmer_id);
      const opsBy = byDbId(filterJdEvidenceOps(cOps || []).rows, r => r.farmer_id);
      const apprBy = byDbId(apprAll, r => r.farmer_id);
      const hmcBy = byDbId(cHmc, r => r.farmer_id);
      covByFarmer.forEach(({ farmer }) => {
        const id = farmer.id;
        if (!(ftfBy[id] || opsBy[id])) return;
        const fcF = buildFieldCoverage({ crop, cropYear: year, policyFields: pfBy[id] || [], appraisals: apprBy[id] || [],
          machineRows: ftfBy[id] || [], machineOps: opsBy[id] || [], hmcFields: hmcBy[id] || [],
          partialHarvest: true, claimMatching: true });
        combineByFarmer[id] = grainCombineOf(fcF);
      });
    }
    covByFarmer.forEach(({ farmer, rows: crows }) => {
      const cov = summarizeClaimCoverage(crows, crop, year, useSnap ? null : claimUnits);
      // Only farms that actually had INSURED ACRES for this crop year — i.e. a computable
      // guarantee (aph×cov×acres, or liability-derived). "Policy but no acres" rows
      // (guaranteeBu null — verified 27 of 129 wheat, all with zero acres AND zero
      // liability) are dropped; they aren't really insured wheat this year.
      if (!cov || (!cov.guaranteeBu && !cov.guaranteeTon)) return;
      const inp = inpByFarmer[farmer.id] || null;
      // THE SAME ENGINE AND THE SAME SOURCE KEYS AS THE DRILL-IN. Appraised bushels are now
      // counted here too (they always were in the drill-in, with its toggle on by default),
      // and machine yield is handed in only to be compared, never added.
      const jdF = jdByFarmer[farmer.id];
      const calc = computeHarvestClaim(cov, harvestSourcesFromInputs(tkByFarmer[farmer.id] || [], inp,
        apprByFarmer[farmer.id] && apprByFarmer[farmer.id].bu > 0 ? apprByFarmer[farmer.id] : null,
        jdF && jdF.totalProd > 0 ? { bu: jdF.totalProd, acres: jdF.totalAcres } : null,
        combineByFarmer[farmer.id] || null), harvest?.price ?? null);
      const tr = trByFarmer[farmer.id] || null;
      const r = { kind: 'harvest', farmer, cov, calc, tracking: tr, reported: tr?.reported || null, hasTickets: calc.hasTickets, claim: claimByFarmer[farmer.id] || null, chopNotice: !!chopByFarmer[farmer.id], jd: jdByFarmer[farmer.id] || null, appr: apprByFarmer[farmer.id] || null };
      // ⚠ CAN THIS ROW'S ESTIMATE BE FROZEN YET — a different question from "is it under".
      // Kevin: *"I want to have an estimate as soon as we have all the data."* The screener
      // deliberately runs on a RUNNING futures average before RMA publishes, which is right
      // for "open a claim or not" and wrong for a frozen estimate: a number latched against a
      // moving price forecasts the market, not the carrier's arithmetic, so when the
      // comparison later fires the delta says nothing about whether the AIP made a mistake.
      // `harvest.isFinal` is what separates the two, and it is already resolved above.
      r.ready = estimateReadiness({ claimType: 'PRODUCTION', guaranteeBu: cov.guaranteeBu,
        production: calc.production > 0 ? calc.production : null, productionPartial: calc.partial === true,
        harvestPrice: harvest?.price ?? null, harvestPriceFinal: harvest?.isFinal === true });
      r.flag = rowFlag(r);
      out.push(r);
    });
    out.sort((a, b) => (Number(b.flag) - Number(a.flag)) || String(a.farmer.name).localeCompare(String(b.farmer.name)));
    setRows(out);
  }

  async function loadPP() {
    const fails = [];
    const [pols, ppAll, trackAll, inds, indLines, ppUnits, ppCov, ppPrices, ppLoss] = await Promise.all([
      sbFetch('policies', { order: 'id.asc', select: 'id,farmer_id,crop_year,aip,policy_number,file_last_seen_at,state' }).catch(rosterReadFailed(fails, 'policies')),
      sbFetch('pp_eligibility', { order: 'id.asc', select: 'id,policy_id,county,crop,type,max_acres,planted_acres,remaining_acres,pp_liability_per_acre' }).catch(rosterReadFailed(fails, 'pp_eligibility')),
      sbFetch('claim_tracking', { order: 'id.asc', filters: [`crop_year=eq.${year}`, `claim_type=eq.${claimType}`] }).catch(rosterReadFailed(fails, 'claim_tracking')),
      sbFetch('indemnities', { select: 'policy_id,crop,crop_year,status,claim_stages,claim_number,insured_intention_codes', order: 'id.asc', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'indemnities')),
      // The paid lines. Only the three columns the stage rule reads — a claim
      // number, its stage, and the year — so the query supplies exactly what
      // the rule consumes and nothing more.
      sbFetch('indemnity_lines', { select: 'loss_key_base,stage_code,crop_year', order: 'id.asc', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'indemnity_lines')),
      // The adjuster's expected payment comes from the SAME engine the Adjuster panel runs
      // (ppCells -> adjudicateFarm -> ppRate.js), so it needs the same inputs: the units
      // (APH, level, share, acreage status), the coverage terms and RMA's prices.
      sbFetch('crop_coverage_units', { order: 'id.asc', select: 'id,policy_id,county,crop,type_code,practice_code,plan,acres,aph_yield,coverage_level,share,is_prevent_plant,liability,acreage_status', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'crop_coverage_units')),
      sbFetch('crop_coverage', { order: 'id.asc', select: 'id,policy_id,county,crop,type_code,mpci_plan_code,insurance_options,price_election_pct,iif_key' }).catch(rosterReadFailed(fails, 'crop_coverage')),
      sbFetch('rma_prices', { order: 'id.asc', select: 'commodity_code,state_code,type_code,practice_code,projected_price', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'rma_prices')),
      // What the carrier PAID on prevent-plant stages — its own P21 worksheet lines, the
      // same source the Claims cross-check compares against.
      sbFetch('m13_loss_detail', { order: 'id.asc', select: 'aip,crop_year,claim_number,iif_key,stage_code,aip_indemnity,synced_at', filters: [`crop_year=eq.${year}`] }).catch(rosterReadFailed(fails, 'm13_loss_detail')),
    ]);
    if (fails.length) { setLoadErr(fails); setRows([]); return; }
    // ── PER FARM: PP ACRES THE AIP REPORTS, THE ADJUSTER'S EXPECTED PAYMENT, AND PAID ──
    // Kevin, 2026-10-19: the "PP liability on eligible acres" dollars (a hypothetical
    // maximum, ~$49.8M book-wide) confused more than they told. What a reader acts on is the
    // prevent-plant acres actually reported and, where a claim exists, what the adjuster
    // expects against what the carrier paid. The expected figure is THE engine
    // (adjudicateFarm), whole farm at once so a roll to another policy's crop is seen.
    const ppByFarm = new Map();
    try {
      const yearPols = pols.filter(p => Number(p.crop_year) === Number(year));
      const built = buildPpCells({ units: ppUnits, coverages: ppCov, eligibility: ppAll,
        policies: yearPols, priceIndex: buildPriceIndex(ppPrices, (code) => RMA_CROPS[code]),
        stateFipsOf: ppStateFips, cropYear: year });
      const groupsByFarm = new Map();
      for (const [pid, g] of built) {
        const fid = g.policy.farmer_id;
        if (!groupsByFarm.has(fid)) groupsByFarm.set(fid, []);
        groupsByFarm.get(fid).push({ policy: { ...g.policy, id: pid }, cells: g.cells,
          model: buildModel({ cells: g.cells, cropYear: year, state: g.policy.state,
            resolveFactor: (input) => resolvePpFactor(input) }) });
      }
      const iifPolicy = new Map((ppCov || []).filter(c => c.iif_key != null).map(c => [String(c.iif_key), c.policy_id]));
      const polFid = new Map(yearPols.map(p => [p.id, p.farmer_id]));
      const paidByFarm = new Map();
      for (const l of cxCurrentLines(ppLoss || []).kept) {
        if (!isPpStage(l.stage_code)) continue;
        const fid = polFid.get(iifPolicy.get(String(l.iif_key)));
        if (!fid) continue;
        paidByFarm.set(fid, (paidByFarm.get(fid) || 0) + (Number(l.aip_indemnity) || 0));
      }
      for (const [fid, groups] of groupsByFarm) {
        const reportedAc = groups.reduce((s, g) => s + g.cells.reduce((t, c) => t + (Number(c.ppAcres) || 0), 0), 0);
        if (!(reportedAc > 0.005) && !paidByFarm.has(fid)) continue;
        let expected = 0, held = false;
        for (const det of adjudicateFarm(groups).values()) {
          expected += Number(det.indemnity) || 0;
          if ((det.undeterminedAcres || 0) > 0.05) held = true;
          for (const d of det.determinations || [])
            if (d.eligibilityBasis === 'unknown' && (d.unplacedAcres || 0) > 0.005) held = true;
        }
        ppByFarm.set(fid, { reportedAc, expected: reportedAc > 0.005 ? expected : null, held,
                            paid: paidByFarm.has(fid) ? paidByFarm.get(fid) : null });
      }
    } catch (e) { /* the engine failing leaves these columns blank — never $0 */ }
    const polFarmer = {}; const yearPids = new Set();
    pols.forEach(p => { polFarmer[p.id] = p.farmer_id; if (Number(p.crop_year) === Number(year)) yearPids.add(p.id); });
    // Policies the AIP's own file no longer names — see supersededPolicyIds().
    // Scoped to THIS crop year so a prior year's book cannot judge this one.
    const deadPids = supersededPolicyIds(pols.filter(p => Number(p.crop_year) === Number(year)));
    const claimByFarmer = buildClaimMap(inds, polFarmer, buildLineIndex(indLines));
    const byFarmer = new Map();
    ppAll.forEach(r => {
      if (!yearPids.has(r.policy_id)) return;
      const f = farmerByDbId[polFarmer[r.policy_id]];
      if (!f) return;
      if (!byFarmer.has(f.id)) byFarmer.set(f.id, { farmer: f, rows: [] });
      byFarmer.get(f.id).rows.push(r);
    });
    const trByFarmer = {}; trackAll.forEach(tr => { const f = farmerByDbId[tr.farmer_id]; if (f) trByFarmer[f.id] = tr; });
    setHarvestMeta(null);
    const out = [];
    byFarmer.forEach(({ farmer, rows: prows }) => {
      // PP eligibility is per crop+county and is NOT additive across policies.
      // Sum the practice/type rows WITHIN a policy — those are distinct ground —
      // then take the MAX across policies, never the sum: a grower holding both an
      // R&H and a NAU policy on the same ground has ONE prevent-plant eligibility.
      // Rich Harvest carries Livingston corn 622.6 on BOTH MP-0932654 and
      // NY-944-6063809-26, and this screen was adding them; six crop-county pairs
      // in the CY2026 book double like that. The crop-review loader already
      // applies this rule (see maxElig) — the two copies must not drift again.
      const byPolicy = new Map();
      prows.forEach(r => {
        const k = `${(r.county || '').toUpperCase()}|${(r.crop || '').toUpperCase()}|${r.policy_id || ''}`;
        const g = byPolicy.get(k) || { max: 0, planted: 0, dead: deadPids.has(r.policy_id) };
        const m = Number(r.max_acres) || 0;
        g.max += m;
        g.planted += Number(r.planted_acres) || 0;
        byPolicy.set(k, g);
      });
      // A SUPERSEDED POLICY MUST NOT WIN THE MAX. It froze the day the AIP
      // stopped naming it, so its eligibility is an older answer wearing this
      // year's policy row — Shipley's replaced Bedford corn policy held 95.8
      // acres against the live policy's 82.09, and the MAX took it.
      //
      // Set aside ONLY where a live policy answers for the same crop+county.
      // 14 of the 15 affected cells book-wide have NO live counterpart, so a
      // rule that dropped them outright would BLANK a card rather than correct
      // it. What was set aside is reported on the row, never silently gone.
      const byCell = new Map();
      const setAside = [];
      byPolicy.forEach((g, k) => {
        const cell = k.slice(0, k.lastIndexOf('|'));
        const cur = byCell.get(cell);
        if (!cur) { byCell.set(cell, g); return; }
        // Live outranks superseded whatever the acres say; between two rows of
        // the same standing the bigger eligibility still wins, exactly as before.
        if (cur.dead && !g.dead) { setAside.push({ cell, acres: cur.max }); byCell.set(cell, g); return; }
        if (!cur.dead && g.dead) { setAside.push({ cell, acres: g.max }); return; }
        if (g.max > cur.max) byCell.set(cell, g);
      });
      let maxAc = 0, plantedAc = 0;
      byCell.forEach(g => { maxAc += g.max; plantedAc += g.planted; });
      const tr = trByFarmer[farmer.id] || null;
      const r = { kind: 'pp', farmer, pp: { maxAc, plantedAc, ppAcres: Math.max(0, maxAc - plantedAc), ...(ppByFarm.get(farmer.id) || { reportedAc: 0, expected: null, held: false, paid: null }), counties: [...new Set(prows.map(x => x.county).filter(Boolean))], setAside }, tracking: tr, reported: tr?.reported || null, hasTickets: false, claim: claimByFarmer[farmer.id] || null };
      // The expected payment beside each farm is THE engine (adjudicateFarm above) — the
      // same determination the Adjuster panel shows, never a second formula here. No
      // max-exposure dollars are shown (Kevin, 2026-10-19).
      r.flag = rowFlag(r);
      out.push(r);
    });
    out.sort((a, b) => (Number(b.flag) - Number(a.flag)) || String(a.farmer.name).localeCompare(String(b.farmer.name)));
    setRows(out);
  }

  useEffect(() => {
    let alive = true;
    setLoading(true); setSelected(null); setRows([]); setLoadErr(null);
    (async () => {
      try { if (ct.kind === 'pp') await loadPP(); else await loadHarvest(); }
      catch (e) { if (alive) { setLoadErr([String((e && e.message) || e)]); setRows([]); } }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [year, claimType, farmers, reloadN]);

  // ⚠ THE CAMPAIGN BAR IS ITS OWN READ, ON THE CROP YEAR ALONE — deliberately NOT
  // inside the load above, which re-runs on every campaign switch. The bar must not
  // rebuild itself from the campaign it is currently showing, or switching campaigns
  // would reorder the buttons under the pointer.
  useEffect(() => {
    let alive = true;
    (async () => {
      const snap = await sbFetch('coverage_snapshots', {
        select: 'crop,plan,acres,liability,type', filters: [`crop_year=eq.${year}`],
      }).catch(() => []);
      if (alive) setBookCrops(claimCropsInBook(snap || []));
    })();
    return () => { alive = false; };
  }, [year]);

  async function saveTracking(fId, patch) {
    const row = rows.find(r => r.farmer.id === fId);
    const cur = row?.tracking || {};
    const next = {
      farmer_id: fId, crop_year: year, claim_type: claimType,
      contacted: cur.contacted || false, contacted_date: cur.contacted_date || null,
      reported: cur.reported ?? null, workflow_status: cur.workflow_status ?? null, notes: cur.notes ?? null,
      ...patch,
    };
    setRows(rs => rs.map(r => {
      if (r.farmer.id !== fId) return r;
      const nr = { ...r, tracking: next, reported: next.reported };
      nr.flag = rowFlag(nr);
      return nr;
    }));
    try {
      await sbUpsert('claim_tracking', [{ ...next, updated_at: new Date().toISOString() }], 'farmer_id,crop_year,claim_type');
      setTrackErr(null);
    } catch (e) {
      // NOT saved. Put the row back the way the database still has it, and say so — an
      // optimistic "Claim Filed" that never reached the table reads as handled work.
      setRows(rs => rs.map(r => {
        if (r.farmer.id !== fId) return r;
        const nr = { ...r, tracking: row?.tracking || null, reported: row?.tracking?.reported ?? null };
        nr.flag = rowFlag(nr);
        return nr;
      }));
      setTrackErr({ name: row?.farmer?.name || 'this farm', msg: String((e && e.message) || e) });
    }
  }
  const todayISO = () => new Date().toISOString().slice(0, 10);

  // Summary + filtered view
  const summary = useMemo(() => ({
    farms: rows.length,
    flags: rows.filter(r => r.flag).length,
    openClaims: rows.filter(r => r.claim).length,
    // Grain-insured corn the grower told the adjuster they would CHOP — RMA's
    // own CS intention code off the Notice of Loss. NOT the same question as the
    // grain/silage TYPE filter beside it: these farms are all grain-insured, and
    // that is precisely why an appraisal is required before the chopper runs.
    // One resolver, three counts — the tiles and the filters below ask it the same way.
    chopClaims: rows.filter(r => silageClaimState(r) !== SILAGE_CLAIM_STATE.NONE).length,
    silageClaim: rows.filter(r => silageHasClaim(silageClaimState(r))).length,
    silageNotice: rows.filter(r => silageClaimState(r) === SILAGE_CLAIM_STATE.NOTICE).length,
    tickets: rows.filter(r => r.hasTickets).length,
    contacted: rows.filter(r => r.tracking?.contacted).length,
  }), [rows]);
  const [sortKey, setSortKey] = useState('flag');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); } };
  // Sort value per column. Production returns -1 for "missing" (no tickets / no manual
  // production) so ascending groups them first — the "who are we missing production for" view.
  const sortVal = (r, key) => {
    switch (key) {
      case 'name': return String(r.farmer.name).toLowerCase();
      case 'guarantee': return r.kind === 'harvest' ? ((r.cov?.guaranteeBu || 0) + (r.cov?.guaranteeTon || 0)) : (r.pp?.maxAc || 0);
      case 'ppreported': return r.pp?.reportedAc || 0;
      case 'ppexpected': return r.pp?.expected || 0;
      case 'pppaid': return r.pp?.paid || 0;
      case 'production': { if (r.kind !== 'harvest') return -1; const has = r.hasTickets || (r.calc?.production > 0); if (!has) return -1; return r.cov?.guaranteeBu ? r.calc.production / r.cov.guaranteeBu : r.calc.production; }
      case 'jd': return (r.jd && r.jd.totalAcres) ? r.jd.totalProd / r.jd.totalAcres : -1;
      // Sorted on BUSHELS, not bu/ac: the question this column answers is how much
      // production is already accounted for, and a 5-acre appraisal at 200 bu/ac is not
      // more of an answer than a 300-acre one at 150.
      case 'appr': return (r.appr && r.appr.bu) ? r.appr.bu : -1;
      case 'contacted': return r.tracking?.contacted ? 1 : 0;
      case 'openclaim': return r.claim ? (String(r.claim.status) === 'Paid' ? 1 : 2) : 0;
      case 'status': { const i = CLAIM_WORKFLOW.indexOf(r.tracking?.workflow_status || 'To Review'); return i < 0 ? 0 : i; }
      case 'flag': return r.flag ? 1 : 0;
      default: return 0;
    }
  };
  const view = useMemo(() => {
    // Only rows matching the CURRENT campaign kind. On a campaign switch, `rows` still
    // holds the previous kind for one render before the reload; without this, a harvest
    // row (no r.pp) renders in PP cells and crashes on r.pp.maxAc (and vice-versa).
    let v = rows.filter(r => r.kind === ct.kind);
    if (flagOnly) v = v.filter(r => r.flag);
    // Corn crop type — silage (tons) vs grain (bu). Deadlines differ (silage EOIP Sept 30 vs
    // grain Dec 10), so the type filter is how you isolate the earlier-deadline silage book.
    if (crop === 'Corn' && typeFilter === 'grain') v = v.filter(r => (r.cov?.guaranteeBu || 0) > 0);
    else if (crop === 'Corn' && typeFilter === 'silage') v = v.filter(r => (r.cov?.guaranteeTon || 0) > 0);
    if (statusFilter !== 'all') v = v.filter(r => (r.tracking?.workflow_status || 'To Review') === statusFilter);
    // ⚠ THIS NARROWS A LIST OF FARMS, WHICH IS THE SAFE HALF OF THE AGENT QUESTION.
    // The Outstanding Premium pane has to RE-ROLL its rows per agent, because a
    // farm's premium is split across policies several agents may have written and a
    // plain filter would credit one agent with another's dollars. Nothing here is
    // additive — the question is "does THIS FARM have an appraisal open" — so a
    // farm-level filter cannot mis-attribute a number.
    //
    // The cost, stated rather than hidden: a farm whose policies were written by
    // more than one agent carries ONE resolved agent on its farmer row, so it
    // appears under that agent only.
    if (agentFilter !== 'all') v = v.filter(r => (cmAgentOf(r) || UNATTRIBUTED_AGENT) === agentFilter);
    // ⚠ THE NEGATIVE IS THE WHOLE PRODUCT OF A WORK LIST. Every facet used to be
    // a 'has it' test, so the board could show the 58 farms with a silage
    // appraisal open and not the 141 corn growers without one — which is the
    // call list. Both directions are offered now.
    if (facet === 'tickets') v = v.filter(r => r.hasTickets);
    else if (facet === 'chop') v = v.filter(r => silageClaimState(r) !== SILAGE_CLAIM_STATE.NONE);
    else if (facet === 'chopclaim') v = v.filter(r => silageHasClaim(silageClaimState(r)));
    else if (facet === 'chopnotice') v = v.filter(r => silageClaimState(r) === SILAGE_CLAIM_STATE.NOTICE);
    else if (facet === 'nochop') v = v.filter(r => silageClaimState(r) === SILAGE_CLAIM_STATE.NONE);
    else if (facet === 'noclaim') v = v.filter(r => !r.claim);
    else if (facet === 'jd') v = v.filter(r => !!r.jd);
    else if (facet === 'claim') v = v.filter(r => !!r.claim);
    else if (facet === 'missing') v = v.filter(r => r.kind === 'harvest' && !r.hasTickets && !r.jd);
    else if (facet === 'contacted') v = v.filter(r => r.tracking?.contacted);
    const s = q.trim().toLowerCase();
    if (s) v = v.filter(r => String(r.farmer.name).toLowerCase().includes(s));
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...v].sort((a, b) => {
      const av = sortVal(a, sortKey), bv = sortVal(b, sortKey);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return String(a.farmer.name).localeCompare(String(b.farmer.name));
    });
    // eslint-disable-next-line
  }, [rows, claimType, flagOnly, statusFilter, facet, typeFilter, crop, q, sortKey, sortDir, agentFilter]);

  // ⚠ BUILT FROM THE ROWS, NEVER FROM A HARDCODED ARRAY — the same rule the Coverage
  // Book Scan's option list follows, so an agent who starts writing business appears
  // here on their own rather than waiting for a code change. An unresolved agent is
  // named 'Unattributed' and is SELECTABLE: those farms are somebody's to call, and a
  // filter that can only ever hide them makes the one list nobody owns invisible.
  const agentOptions = useMemo(() => {
    const seen = new Set();
    for (const r of rows) seen.add(cmAgentOf(r) || UNATTRIBUTED_AGENT);
    const known = AGENCY_AGENTS.filter(a => seen.has(a));
    const rest = [...seen].filter(a => !known.includes(a)).sort();
    return [...known, ...rest];
  }, [rows]);

  const bu = n => (n == null || !isFinite(n)) ? '—' : Math.round(n).toLocaleString();
  const money = n => (n == null || !isFinite(n)) ? '—' : '$' + Math.round(n).toLocaleString();
  // The adjuster's expected PP payment (ADMIN-only screen). "partial" when some prevented
  // acres could not be determined — the figure then covers only what was, and says so.
  const ppExpectCell = (r) => r.pp.expected == null ? '—'
    : <span title={r.pp.held ? 'Some prevent-plant acres could not be determined (eligibility or pricing missing) — open the Adjuster panel' : undefined}>
        {money(r.pp.expected)}{r.pp.held ? ' (partial)' : ''}</span>;
  const lbl = { fontSize: 10, color: '#60735f', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' };
  const inStyle = { padding: '5px 7px', border: '1px solid #cdddcd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', background: '#fff' };
  const th = { textAlign: 'left', padding: '7px 8px', fontSize: 10.5, color: '#60735f', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #e4ece4' };
  const td = { padding: '7px 8px', fontSize: 12.5, borderBottom: '1px solid #f0f4f0', verticalAlign: 'middle' };
  const sortTh = (key, label, align) => (
    <th onClick={() => toggleSort(key)} title="Click to sort"
      style={{ ...th, textAlign: align || 'left', cursor: 'pointer', userSelect: 'none', color: sortKey === key ? '#1a2e1a' : '#60735f' }}>
      {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const statusSel = (r, extra) => (
    <select value={r.tracking?.workflow_status || 'To Review'} onClick={e => e.stopPropagation()} onChange={e => saveTracking(r.farmer.id, { workflow_status: e.target.value })} style={{ ...inStyle, ...extra }}>
      {CLAIM_WORKFLOW.map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  );
  const contactedBox = (r) => (
    <label onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11.5, color: '#4a5a4a' }}>
      <input type="checkbox" checked={!!r.tracking?.contacted} onChange={e => saveTracking(r.farmer.id, { contacted: e.target.checked, contacted_date: e.target.checked ? todayISO() : null })} />
      {r.tracking?.contacted ? (r.tracking.contacted_date || 'yes') : ''}
    </label>
  );
  // A chop claim and an ordinary harvest-loss claim are BOTH stage H and looked
  // identical here. `CS` is RMA's own Chop/silage intention code off the P58
  // Notice of Loss (ICE D00067) — the one field that separates them.
  const claimBadge = (r) => {
    // ⚠ THE CHOP APPRAISAL WAS COMPUTED AFTER AN EARLY RETURN, SO THE BOARD
    // CONTRADICTED ITSELF. Kevin, 2026-09-09: "Brian Thompson has a Silage Appraisal
    // claim opened, why is he showing nothing? Same with Hartway. How am i supposed
    // to trust this thing?"
    //
    // Both filed a CS Notice of Loss (BRIAN THOMPSON NY-944-6076116-26 and HARTWAY
    // NY-944-6077846-26, both 2026-08-26), and `rowIsChop` was TRUE for both — they
    // are counted in the "🌾 Chop appraisal 58" tile and they are returned by the
    // "Silage appraisal (chop)" facet. Their ROW printed "none".
    //
    // The cause is that `r.claim` and the chop appraisal answer DIFFERENT QUESTIONS.
    // `r.claim` is scoped to THIS campaign and, once money exists, the money governs —
    // so Brian's settled PREVENT-PLANT payment correctly does not count as a corn
    // HARVEST claim (indMatchesCampaign, and that rule is right). But a filed silage
    // appraisal has nothing to do with which stage his July money was paid under, and
    // returning before the badge was even computed made the count, the filter and the
    // row disagree about the same farm.
    //
    // A chop notice now prints on its own. It is deliberately NOT dressed as a claim
    // status — there is no indemnity row and no dollar behind it — it says what it is.
    // ONE STATE (silageClaimState), one vocabulary: "Silage appraisal" is the noun an agent
    // and a grower use; "chop" stays a filter shorthand only. A sub-agent who cannot read the
    // notice mirror sees NO silage lane at all rather than a confident "none" (chopLaneVisible).
    const st = chopLaneVisible ? silageClaimState(r) : SILAGE_CLAIM_STATE.NONE;
    const silPill = { display: 'inline-block', whiteSpace: 'nowrap', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 10, background: '#fbf0de', color: '#8a5a0f' };
    // ⚠ ONE SHAPE, NOT JUST ONE VOCABULARY (Kevin, 2026-09-23: "some claims say Open Chop
    // some say Chop Appraisal. Make them all uniform how they are displayed"). The words were
    // unified on 2026-09-18 and the LAYOUT was not: a staged claim printed two pills side by
    // side while a notice printed one pill stacked over a muted sub-line, so the same idea
    // arrived in two silhouettes and the reader still had to work out what separated them.
    //
    // Every row is now [CLAIM STATE][SILAGE FACT], in that order, in the same two pill styles.
    // The distinction the 2026-09-18 note insists on is kept and is now carried by the FIRST
    // pill rather than by the shape: a notice with nothing staged reads "○ No claim yet", which
    // is what it is — there is no indemnity row and no dollar behind it — and must never be
    // dressed as an Open claim.
    const claimPill = (bg, col, txt, title) => (
      <span title={title || ''} style={{ display: 'inline-block', whiteSpace: 'nowrap', fontSize: 10.5, fontWeight: 800, padding: '2px 7px', borderRadius: 10, background: bg, color: col }}>{txt}</span>
    );
    const silageTag = (title) => <span style={silPill} title={title}>{SILAGE_WORDS.TAG}</span>;
    if (!r.claim) {
      if (st !== SILAGE_CLAIM_STATE.NOTICE) return <span style={{ fontSize: 11, color: '#b8c4b8' }}>none</span>;
      return (
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {claimPill('#f4f6f4', '#6a7a6a', '○ No claim yet',
            'The grower filed a Notice of Loss stating they intend to CHOP this grain-insured corn (RMA intention code CS). The AIP has not staged a claim for it yet, so there is no claim row and no dollar figure.')}
          {silageTag('The standing corn must be appraised before it goes through a chopper, and that appraisal is the production to count.')}
        </span>
      );
    }
    const paid = st === SILAGE_CLAIM_STATE.PAID || String(r.claim.status) === 'Paid';
    return (
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        {claimPill(paid ? '#e8f6ec' : '#e7f0fa', paid ? '#2d6a2d' : '#1a4a7a',
          paid ? '✓ Paid' : '● ' + r.claim.status,
          r.claim.claim_number ? `Claim #${r.claim.claim_number}` : '')}
        {silageHasClaim(st) && silageTag('The grower told the adjuster they intend to CHOP this grain-insured corn — RMA intention code CS on the Notice of Loss — and the AIP has staged the claim. The standing corn must be appraised before it goes through a chopper, and that appraisal is the production to count.')}
      </span>
    );
  };
  // Guarantee shown in the farm's INSURED unit(s) — grain in bu, silage in tons, never
  // blended. A farm insured as both shows both lines. Subtitle = insured liability ($).
  const guarCell = (r) => {
    const g = r.cov || {};
    const lines = [];
    // ⚠ THE UNIT IS THE CAMPAIGN CROP'S, NOT A HARDCODED "bu". Found by RENDERING the
    // Potatoes campaign with every static check green: the board printed "81,795 bu"
    // over a potato guarantee — the NUMBER right and the LABEL wrong, which is the
    // units-of-measure invariant failing in a caption. Potatoes and onions are cwt,
    // dry beans lbs, grapes and processing beans ton.
    if (g.guaranteeBu) lines.push(<div key="bu">{bu(g.guaranteeBu)} <span style={{ fontWeight: 400, color: '#60735f' }}>{campaignUnit}</span></div>);
    if (g.guaranteeTon) lines.push(<div key="ton">{bu(g.guaranteeTon)} <span style={{ fontWeight: 400, color: '#60735f' }}>tons</span></div>);
    if (!lines.length) return <span style={{ color: '#b34a10', fontSize: 11, fontFamily: 'inherit' }}>coverage incomplete</span>;
    return <>{lines}{g.liability ? <div style={{ fontSize: 10.5, color: '#60735f' }}>{money(g.liability)}</div> : null}</>;
  };
  // Acre-weighted APH for a row (shared basis so Scale Tickets and JD Monitor compare on
  // the same yardstick — % of APH, not % of guarantee, which the coverage level distorts).
  const aphWtdOf = (r) => {
    const us = (r.cov?.units || []).filter(u => u.aph > 0 && u.acres > 0);
    return us.length ? us.reduce((s, u) => s + u.aph * u.acres, 0) / us.reduce((s, u) => s + u.acres, 0) : null;
  };
  // Small colored pill that states over/under the GUARANTEE in words — the actual claim
  // signal. Replaces the old bare ▼/▲ that read like "down X bushels".
  const guarPill = (production, guar) => {
    if (!(guar > 0)) return null;
    const pct = production / guar;
    const under = pct < 1;
    return (
      <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 800, padding: '1px 7px', borderRadius: 9, whiteSpace: 'nowrap', background: under ? '#fbeae5' : '#e8f6ec', color: under ? '#b3300f' : '#2d6a2d' }}>
        {under ? 'UNDER' : 'OVER'} · {Math.round(pct * 100)}% of guar
      </span>
    );
  };
  // ⚠ THE COLUMN IS PRODUCTION, NOT SCALE TICKETS. calc.production is tickets (or the typed
  // delivered figure) + stored / seed / fed + appraised bushels (computeHarvestClaim). Headed
  // "Scale Tickets", an appraisal-only farm read as if its appraisal had been imported as
  // tickets (Kevin, Harkins 2026-11-03). The tag names what the figure is made of, read off the
  // engine's own parts — no arithmetic here, and the sort key is unchanged.
  const productionSourceTag = (c) => {
    const parts = [];
    if (c.delivered > 0 || c.hasTickets) parts.push(c.lines && c.lines.some((l) => l.source === 'override') ? 'delivered (typed)' : 'tickets');
    if (c.extra > 0) parts.push('on farm');
    if (c.appraisedBu > 0) parts.push('appraisal');
    if (c.combineCounted && c.combineBu > 0) parts.push('JD combine (grain)');
    if (!parts.length) return null;
    return parts.join(' + ') + (c.hasTickets ? '' : ' · no tickets yet');
  };
  const measuredCell = (r) => {
    if (r.kind !== 'harvest') return <span style={{ color: '#b8c4b8' }}>—</span>;
    if (!r.hasTickets && !(r.calc.production > 0)) return <span style={{ fontSize: 11, color: '#b8c4b8' }}>no tickets</span>;
    const insAc = r.cov?.acres || null;                             // scale-ticket yield/ac = production ÷ insured acres
    const ypa = insAc ? r.calc.production / insAc : null;
    const aphWtd = aphWtdOf(r);
    const pctAph = (ypa != null && aphWtd) ? ypa / aphWtd : null;   // % of APH — same basis as JD
    return (
      <div style={{ lineHeight: 1.35 }}>
        <div style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: 13, color: '#1a2e1a' }}>{bu(r.calc.production)} <span style={{ fontWeight: 400, color: '#60735f', fontSize: 11 }}>bu</span></div>
        {productionSourceTag(r.calc) && <div style={{ fontSize: 10, color: '#60735f', marginTop: 1 }}>{productionSourceTag(r.calc)}</div>}
        {/* ⚠ APPRAISED BUSHELS ON PART OF THE GROUND ARE A FLOOR, NOT A SHORTFALL. The engine
            withholds the read (calc.partial) and so does this cell — the drill-in's own rule. */}
        {r.calc.partial
          ? <div style={{ fontSize: 10, color: '#7a6a2a', marginTop: 3 }}>so far — covers {bu(r.calc.acreScopedAcres)} of {bu(r.calc.plantedAcres)} planted ac</div>
          : <div style={{ marginTop: 3 }}>{guarPill(r.calc.production, r.cov?.guaranteeBu)}</div>}
        {ypa != null && !r.calc.partial && <div style={{ fontSize: 10, color: '#8a9a88', marginTop: 3 }}>{ypa.toFixed(0)} {campaignUnit}/ac{pctAph != null ? ` · ${Math.round(pctAph * 100)}% APH` : ''}</div>}
        {/* Machine yield beside production — admin only, and never added to it. */}
        {isCurrentUserAdmin() && r.calc.machineCheck && r.calc.machineCheck.flag && (
          <div style={{ fontSize: 10, fontWeight: 700, color: '#8a3324', marginTop: 3 }}
            title="John Deere yield-monitor bushels against measured production (tickets or delivered, plus stored, seed and fed). More than 10% apart. Not added to production.">
            ⚠ machine {Math.round(r.calc.machineCheck.diffPct * 100)}% off measured
          </div>
        )}
        {/* ⚠ WHY THIS ROW CANNOT BE FROZEN YET, said in the row rather than left to be
            inferred from a missing button. The common reason all season is that RMA has
            not published the harvest price, and "waiting on RMA" and "we are missing your
            production" are different jobs for the agent — one is a phone call and the
            other is a calendar. A READY row says nothing: an estimate being possible is
            not news, and a badge on every row is a badge nobody reads. */}
        {r.ready && r.ready.status !== CE_READY.READY && (
          <div style={{ fontSize: 10.5, color: r.ready.status === CE_READY.NOT_ESTIMABLE ? '#a5461b' : '#8a6d1f', marginTop: 3, lineHeight: 1.3 }}>
            {describeReadiness(r.ready)}
          </div>
        )}
      </div>
    );
  };

  // JD yield-monitor production (all harvested acres) as a reference cross-check. Estimate,
  // not claim-of-record — colored vs guarantee but does NOT drive the flag (tickets do).
  const jdCell = (r) => {
    if (r.kind !== 'harvest' || !r.jd || !r.jd.totalAcres) return <span style={{ fontSize: 11, color: '#b8c4b8' }}>—</span>;
    const ypa = r.jd.totalProd / r.jd.totalAcres;
    // Lead with yield/ac vs the acre-weighted APH — the reliable read. Production % of
    // guarantee misleads whenever JD acres < insured (partial connection / custom-harvest),
    // so we flag acre coverage instead of showing the deceptive %.
    const us = (r.cov?.units || []).filter(u => u.aph > 0 && u.acres > 0);
    const aphWtd = us.length ? us.reduce((s, u) => s + u.aph * u.acres, 0) / us.reduce((s, u) => s + u.acres, 0) : null;
    const vsAph = aphWtd ? ypa / aphWtd : null;
    // ⚠ PLANTED, NOT INSURED. JD's totalAcres is HARVESTED ground, so the honest
    // denominator is what was planted — a prevent-plant acre was never available to
    // harvest, and counting it makes a fully-covered farm read as a partial connection
    // ("⚠ only N% of acres") when the monitor in fact saw everything there was to see.
    const insAc = (r.cov?.plantedAcres != null ? r.cov.plantedAcres : r.cov?.acres) || null;
    const acresCov = insAc ? r.jd.totalAcres / insAc : null;
    const lowCov = acresCov != null && acresCov < 0.75;
    // RED = below the GUARANTEE yield/ac (APH × coverage), i.e. a real potential loss — NOT
    // merely below APH. A yield can be under APH but comfortably over the guarantee.
    // Guarantee per acre is also over PLANTED acres: the bushel guarantee has to be
    // met off the ground that was actually planted.
    const guarYpa = (r.cov?.guaranteeBu && insAc) ? r.cov.guaranteeBu / insAc : null;
    const under = guarYpa != null && ypa < guarYpa;
    return (
      <div style={{ lineHeight: 1.35 }}
        title={`JD ${bu(r.jd.totalProd)} ${campaignUnit} on ${r.jd.totalAcres.toFixed(0)} ac${insAc ? ` of ${insAc.toFixed(0)} insured (${Math.round((acresCov || 0) * 100)}%)` : ''}${guarYpa ? ` · guarantee ${guarYpa.toFixed(0)} ${campaignUnit}/ac` : ''} — owner-attributed monitor estimate, not claim-of-record`}>
        <div style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: 13, color: guarYpa == null ? '#4a5a4a' : (under ? '#b3300f' : '#2d6a2d') }}>{bu(r.jd.totalProd)} <span style={{ fontWeight: 400, color: '#60735f', fontSize: 11 }}>{campaignUnit} est</span></div>
        <div style={{ fontSize: 10, color: '#8a9a88', marginTop: 3 }}>{ypa.toFixed(0)} {campaignUnit}/ac{vsAph != null ? ` · ${Math.round(vsAph * 100)}% APH` : ''}</div>
        {lowCov && <div style={{ fontSize: 10, fontWeight: 700, color: '#b3300f', marginTop: 1 }}>⚠ only {Math.round(acresCov * 100)}% of acres</div>}
      </div>
    );
  };

  // APPRAISED production — the adjuster's own figure on acres that will not produce a
  // scale ticket (chopped, destroyed, released). Kevin, 2026-09-30: "I thought we were
  // adding an appraisal column."
  //
  // ⚠ IT IS STRUCTURALLY PARTIAL, AND THE PILL REFLECTS THAT. An appraisal covers the
  // acres being released, never the whole farm — so unlike Scale Tickets this column must
  // NOT show an UNDER/red signal. Measured 2026-09-30: LANG 36.7 of 36.7 planted acres
  // (100%), ROGERS 308 of 1,108 (28%). Treated like tickets, Rogers reads "40% of
  // guarantee · UNDER · claim likely" on 28% of his acres — a false claim flag on a farm
  // nobody has measured yet.
  //
  // The asymmetry is what makes it useful: a partial reading is a valid FLOOR. If the
  // appraised bushels ALREADY clear the guarantee the answer is settled no matter what
  // the unmeasured acres did (LANG: 4,913 bu against a 4,388 bu guarantee — over, done).
  // If they do not, the answer is UNKNOWN, never "under".
  //
  // ⚠ DENOMINATOR IS PLANTED ACRES. Insured includes prevent plant, and a PP acre can
  // carry neither an appraisal nor a ticket — the same defect this screen's drill-in
  // carried until today.
  const apprCell = (r) => {
    if (r.kind !== 'harvest' || !r.appr || (!r.appr.bu && !r.appr.otherUnitLines)) {
      return <span style={{ fontSize: 11, color: '#b8c4b8' }}>—</span>;
    }
    const a = r.appr;
    const planted = r.cov?.plantedAcres != null ? r.cov.plantedAcres : (r.cov?.acres || null);
    const pctAc = planted && a.acres > 0 ? a.acres / planted : null;
    const ypa = a.acres > 0 ? a.bu / a.acres : null;
    const guar = r.cov?.guaranteeBu || null;
    const clears = guar != null && a.bu >= guar;          // a valid floor: settled over
    return (
      <div style={{ lineHeight: 1.35 }}
        title={`Appraised ${bu(a.bu)} bu on ${a.acres.toFixed(1)} ac${planted ? ` of ${planted.toFixed(0)} planted (${Math.round((pctAc || 0) * 100)}%)` : ''}${guar ? ` · guarantee ${bu(guar)} bu` : ''} — the adjuster's figure on acres that will not produce a scale ticket. An appraisal covers only the released acres, so it is a FLOOR, never a shortfall.`}>
        <div style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: 13, color: clears ? '#2d6a2d' : '#4a5a4a' }}>
          {bu(a.bu)} <span style={{ fontWeight: 400, color: '#60735f', fontSize: 11 }}>bu appr</span>
        </div>
        <div style={{ fontSize: 10, color: '#8a9a88', marginTop: 3 }}>
          {ypa != null ? `${ypa.toFixed(0)} ${campaignUnit}/ac` : '—'}{pctAc != null ? ` · ${Math.round(pctAc * 100)}% of planted ac` : ''}
        </div>
        {clears
          ? <div style={{ fontSize: 10, fontWeight: 700, color: '#2d6a2d', marginTop: 1 }}>clears the guarantee</div>
          : pctAc != null && pctAc < 0.95
            ? <div style={{ fontSize: 10, color: '#7a6a2a', marginTop: 1 }}>partial — rest not measured</div>
            : null}
        {a.otherUnitLines > 0 && (
          <div style={{ fontSize: 10, color: '#8a6d1f', marginTop: 1 }}>+{a.otherUnitLines} line{a.otherUnitLines > 1 ? 's' : ''} {a.silageInsuredLines > 0 ? 'silage-insured or in tons/lb' : 'in tons/lb'}, not added</div>
        )}
      </div>
    );
  };

  const selectedRow = rows.find(r => r.farmer.id === selected && r.kind === ct.kind);

  return (
    <div>
      {/* The one-line purpose stays visible; the workflow explanation is one click
          away instead of two lines sitting above the board on every visit. */}
      <details style={{ fontSize: 12.5, color: '#60735f', margin: '-4px 0 14px', lineHeight: 1.5 }}>
        <summary style={{ cursor: 'pointer' }}>Every farmer with coverage for the campaign, tracked to a decision · <span style={{ textDecoration: 'underline' }}>how this works</span></summary>
        <div style={{ marginTop: 4 }}>Import scale tickets (in a farm's detail) to auto-compute over/under, mark who you've talked to, and set each farmer's <strong style={{ color: '#1a2e1a' }}>Status</strong>. The board <strong style={{ color: '#b3300f' }}>🚩 flags</strong> anyone under guarantee (from tickets, or when you set "Under — Needs Claim") with no matching open claim on the Active tab; setting "Over — No Claim" clears it.</div>
      </details>

      {/* Campaign selector */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        {/* The four core campaigns are buttons; every other crop the book carries
            (grapes, cabbage, onions… 13 more) sits in one "More crops" picker.
            Seventeen buttons in three rows buried the four that get used. The
            selected campaign is always a button, so it never hides in the menu. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {Object.keys(campaigns).filter(k => !campaigns[k].derived || k === claimType).map(k => (
            <button key={k} onClick={() => setClaimType(k)} title={campaigns[k].basis === 'yield' ? 'Yield plan (APH/YP) — production guarantee only, no revenue guarantee' : undefined} style={{
              padding: '7px 12px', borderRadius: 8, border: claimType === k ? '2px solid #2d6a2d' : '1px solid #d8e4d8', cursor: 'pointer',
              background: claimType === k ? '#eef6ef' : '#fff', color: '#1a2e1a', fontWeight: claimType === k ? 800 : 600, fontSize: 12.5,
            }}>{campaigns[k].emoji} {campaigns[k].label}</button>
          ))}
          {Object.keys(campaigns).some(k => campaigns[k].derived && k !== claimType) && (
            <select value="" onChange={e => { if (e.target.value) setClaimType(e.target.value); }}
              style={{ ...inStyle, padding: '7px 9px', fontSize: 12.5, fontWeight: 600, color: '#1a2e1a' }}>
              <option value="">More crops…</option>
              {Object.keys(campaigns).filter(k => campaigns[k].derived && k !== claimType).map(k => (
                <option key={k} value={k}>{campaigns[k].label}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <div style={lbl}>Crop year</div>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inStyle, padding: '7px 9px' }}>
            {[CURRENT_CROP_YEAR + 1, CURRENT_CROP_YEAR, CURRENT_CROP_YEAR - 1, CURRENT_CROP_YEAR - 2].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {harvestMeta && (
          <div style={{ marginLeft: 'auto', fontSize: 11.5, color: '#4a5a4a', textAlign: 'right' }}>
            <div style={lbl}>Harvest price</div>
            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 14 }}>${Number(harvestMeta.price).toFixed(2)}</span>
            <span style={{ fontSize: 10.5, fontWeight: 800, padding: '1px 5px', borderRadius: 4, marginLeft: 5, background: harvestMeta.isFinal ? '#e8f6ec' : '#fdf6e7', color: harvestMeta.isFinal ? '#2d6a2d' : '#7a5b00' }}>{harvestMeta.isFinal ? 'FINAL' : 'EST'}</span>
          </div>
        )}
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3,minmax(0,1fr))' : `repeat(${ct.kind === 'harvest' && crop === 'Corn' && chopLaneVisible ? 7 : 5},1fr)`, gap: isMobile ? 6 : 10, marginBottom: 14 }}>
        {[
          { k: 'Farms in campaign', v: summary.farms, c: '#1a2e1a' },
          { k: '🚩 Needs claim', v: summary.flags, c: '#b3300f' },
          { k: 'Has claim', v: summary.openClaims, c: '#1a4a7a' },
          ...(ct.kind === 'harvest' && crop === 'Corn' && chopLaneVisible
            ? [{ k: SILAGE_WORDS.CLAIM_SHORT, v: summary.silageClaim, c: '#8a5a0f', hint: SILAGE_WORDS.CLAIM_HINT },
               { k: SILAGE_WORDS.NOTICE_SHORT, v: summary.silageNotice, c: '#8a7a5a', hint: SILAGE_WORDS.NOTICE_HINT }] : []),
          { k: ct.kind === 'harvest' ? 'Tickets in' : 'Eligible', v: ct.kind === 'harvest' ? summary.tickets : summary.farms, c: '#2d6a2d' },
          { k: 'Contacted', v: summary.contacted, c: '#6b4f00' },
        ].map((s, i) => (
          <Card key={i} style={{ textAlign: 'center', padding: isMobile ? '8px 6px' : '12px 8px', cursor: s.k.includes('Needs') ? 'pointer' : 'default' }} onClick={s.k.includes('Needs') ? () => setFlagOnly(f => !f) : undefined}>
            <div style={{ ...lbl, marginBottom: 3 }}>{s.k}</div>
            <div style={{ fontSize: isMobile ? 19 : 22, fontWeight: 800, color: s.c, fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
            {/* The short tile label carries its own qualifier, so "Silage appraisal" and
                "Silage notice" cannot be mistaken for each other at a glance. */}
            {s.hint && <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 2 }}>{s.hint}</div>}
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search farm…" style={{ ...inStyle, padding: '7px 10px', flex: isMobile ? '1 1 100%' : '0 0 200px' }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inStyle, padding: '7px 9px', flex: isMobile ? '1 1 46%' : '0 0 auto' }}>
          <option value="all">All statuses</option>
          {CLAIM_WORKFLOW.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {/* Show: quick facet filter (works on mobile + desktop) */}
        <select value={facet} onChange={e => setFacet(e.target.value)} style={{ ...inStyle, padding: '7px 9px', flex: isMobile ? '1 1 46%' : '0 0 auto' }}>
          <option value="all">Show: all</option>
          {ct.kind === 'harvest' && <option value="tickets">Has scale tickets</option>}
          {ct.kind === 'harvest' && crop === 'Corn' && chopLaneVisible && <option value="chop">{SILAGE_WORDS.EITHER_LONG}</option>}
          {ct.kind === 'harvest' && crop === 'Corn' && chopLaneVisible && <option value="chopclaim">{SILAGE_WORDS.CLAIM_LONG}</option>}
          {ct.kind === 'harvest' && crop === 'Corn' && chopLaneVisible && <option value="chopnotice">{SILAGE_WORDS.NOTICE_LONG}</option>}
          {/* ⚠ GATED ON chopLaneVisible LIKE ITS POSITIVE TWIN. The chop lane reads the
              raw AIP mirror, which carries a single admin SELECT policy — so for a scoped
              sub-agent every row would read "no appraisal" and the page would hand them a
              confident call list that is really "we cannot see the notices". */}
          {ct.kind === 'harvest' && crop === 'Corn' && chopLaneVisible && <option value="nochop">{SILAGE_WORDS.NONE_LONG}</option>}
          {ct.kind === 'harvest' && <option value="jd">Has JD yield</option>}
          {ct.kind === 'harvest' && <option value="missing">Missing production</option>}
          <option value="claim">Has a claim</option>
          <option value="noclaim">No claim opened</option>
          <option value="contacted">Contacted</option>
        </select>
        <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)}
          title="Narrow the board to one agent's farms" style={{ ...inStyle, padding: '7px 9px', flex: isMobile ? '1 1 46%' : '0 0 auto' }}>
          <option value="all">All agents</option>
          {agentOptions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {/* Crop TYPE — corn only (grain bu vs silage tons); deadlines differ, so this isolates
            the silage book that hits its end-of-insurance-period first (Sept 30). */}
        {crop === 'Corn' && (
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} title="Grain (bu) vs silage (tons) — silage insurance ends Sept 30, grain Dec 10" style={{ ...inStyle, padding: '7px 9px', flex: isMobile ? '1 1 46%' : '0 0 auto' }}>
            <option value="all">All types</option>
            <option value="grain">🌽 Grain only</option>
            <option value="silage">🌱 Silage only</option>
          </select>
        )}
        {/* Sort — mobile only (desktop sorts via column headers) */}
        {isMobile && (
          <div style={{ display: 'flex', gap: 6, flex: '1 1 100%' }}>
            <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={{ ...inStyle, padding: '7px 9px', flex: 1 }}>
              <option value="flag">Sort: Needs-claim</option>
              <option value="name">Sort: Farm name</option>
              <option value="guarantee">Sort: Guarantee</option>
              {ct.kind === 'harvest' && <option value="production">Sort: Production</option>}
              {ct.kind === 'harvest' && <option value="jd">Sort: JD yield</option>}
              <option value="openclaim">Sort: Claim</option>
              <option value="contacted">Sort: Contacted</option>
              <option value="status">Sort: Status</option>
            </select>
            <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')} title="Toggle sort direction"
              style={{ ...inStyle, padding: '7px 12px', cursor: 'pointer', fontWeight: 800 }}>{sortDir === 'asc' ? '↑' : '↓'}</button>
          </div>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: flagOnly ? '#b3300f' : '#4a5a4a', cursor: 'pointer' }}>
          <input type="checkbox" checked={flagOnly} onChange={e => setFlagOnly(e.target.checked)} /> 🚩 Needs claim only
        </label>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#60735f' }}>{view.length} of {rows.length}</span>
      </div>

      {/* Corn change-of-intent deadline reminder — the whole reason type matters here. */}
      {crop === 'Corn' && (
        <div style={{ fontSize: 11.5, color: '#7a5b00', background: '#fdf8ea', border: '1px solid #eadfb8', borderRadius: 8, padding: '8px 11px', marginBottom: 12, lineHeight: 1.5 }}>
          ⏳ <strong>Corn end of insurance period:</strong> <strong>Sept 30 (silage)</strong> · <strong>Dec 10 (grain)</strong>. A <strong>change of intent</strong> — silage-insured corn harvested for grain, or grain-insured corn chopped for silage — must be reported to the AIP and <strong>appraised before</strong> the crop is put to the other use, with a Notice of Loss within <strong>72 hours</strong> of discovery. Filter to <strong>🌱 Silage</strong> to work the earlier Sept 30 book first.
        </div>
      )}

      {trackErr && (
        <div role="alert" style={{ fontSize: 12, color: '#8a3324', background: '#fbeeea', border: '1px solid #e8c4b8', borderRadius: 8, padding: '8px 11px', marginBottom: 12 }}>
          ⚠ <strong>Not saved:</strong> the follow-up change for {trackErr.name} did not save and was undone — {trackErr.msg}
          <button onClick={() => setTrackErr(null)} style={{ marginLeft: 8, border: 'none', background: 'transparent', color: '#8a3324', cursor: 'pointer', fontWeight: 700 }}>Dismiss</button>
        </div>
      )}
      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#60735f' }}><UiSpinner />Loading {ct.label.toLowerCase()} roster…</div>
      ) : loadErr ? (
        <Card role="alert" style={{ padding: '20px 22px', border: '1px solid #e8c4b8', background: '#fbeeea' }}>
          <div style={{ fontWeight: 800, color: '#8a3324' }}>Couldn't load the {ct.label.toLowerCase()} roster for {year}</div>
          <div style={{ color: '#6b3a2e', fontSize: 12.5, marginTop: 4 }}>A read failed, so no farm is shown rather than a roster missing its claims or follow-ups:</div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#6b3a2e', fontSize: 12 }}>{loadErr.map((m, i) => <li key={i}>{m}</li>)}</ul>
          <button onClick={() => setReloadN(n => n + 1)} style={{ marginTop: 10, padding: '6px 12px', borderRadius: 6, border: '1px solid #cdddcd', background: '#fff', fontWeight: 700, color: '#2d4a2d', cursor: 'pointer' }}>Try again</button>
        </Card>
      ) : rows.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '40px 24px', border: '1px dashed #c8d8c8' }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>{ct.emoji}</div>
          <div style={{ fontWeight: 800, color: '#1a2e1a' }}>No {ct.label.toLowerCase()} roster for {year}</div>
          <div style={{ color: '#60735f', fontSize: 12.5, marginTop: 4 }}>No farmers with {ct.kind === 'pp' ? 'prevent-plant eligibility' : `base ${crop.toLowerCase()} coverage`} were found for this crop year.</div>
        </Card>
      ) : isMobile ? (
        <div>
          {view.map(r => (
            <div key={r.farmer.id} onClick={() => setSelected(sel => sel === r.farmer.id ? null : r.farmer.id)}
              style={{ border: '1px solid #e4ece4', borderLeft: `4px solid ${r.flag ? '#b3300f' : (rowHandled(r) ? '#2d6a2d' : '#e4ece4')}`, borderRadius: 10, padding: 12, marginBottom: 10, background: selected === r.farmer.id ? '#f2f9f2' : (r.flag ? '#fdf5f2' : '#fff') }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <div style={{ fontWeight: 800, color: '#1a2e1a', fontSize: 14, lineHeight: 1.25 }}>{r.farmer.name}</div>
                <div style={{ flexShrink: 0 }}>{r.flag ? <span style={{ fontSize: 16 }}>🚩</span> : (rowHandled(r) ? <span style={{ color: '#2d6a2d', fontWeight: 800 }}>✓</span> : null)}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', marginBottom: 10 }}>
                <div>
                  <div style={lbl}>{ct.kind === 'harvest' ? 'Guarantee' : 'PP eligible'}</div>
                  <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 12.5, fontWeight: 700, color: '#1a2e1a' }}>
                    {ct.kind === 'harvest'
                      ? guarCell(r)
                      : <>{bu(r.pp.maxAc)} ac <span style={{ fontWeight: 400, color: '#60735f' }}>· {bu(r.pp.reportedAc)} ac PP reported{r.pp.expected != null ? <> · expect {ppExpectCell(r)} · paid {money(r.pp.paid)}</> : null}</span></>}
                  </div>
                </div>
                <div><div style={lbl}>Claim</div><div style={{ marginTop: 2 }}>{claimBadge(r)}</div></div>
                {ct.kind === 'harvest' && <div><div style={lbl}>Production</div><div style={{ marginTop: 2 }}>{measuredCell(r)}</div></div>}
                {ct.kind === 'harvest' && <div><div style={lbl}>JD monitor</div><div style={{ marginTop: 2 }}>{jdCell(r)}</div></div>}
                {ct.kind === 'harvest' && <div><div style={lbl}>Appraised</div><div style={{ marginTop: 2 }}>{apprCell(r)}</div></div>}
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid #f0f4f0', paddingTop: 8 }} onClick={e => e.stopPropagation()}>
                {contactedBox(r)}
                <div style={{ flex: '1 1 150px' }}>{statusSel(r, { width: '100%' })}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e4ece4', borderRadius: 10, background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 'auto' }}>
            <thead>
              <tr>
                {sortTh('name', 'Farm')}
                {ct.kind === 'harvest' ? <>
                  {sortTh('guarantee', 'Guarantee', 'right')}
                  {sortTh('production', 'Production', 'right')}
                  {sortTh('jd', 'JD Monitor', 'right')}
                  {sortTh('appr', 'Appraised', 'right')}
                </> : <>
                  {sortTh('guarantee', 'PP elig ac', 'right')}
                  {sortTh('ppreported', 'PP acres reported', 'right')}
                  {sortTh('ppexpected', 'Adjuster expects', 'right')}
                  {sortTh('pppaid', 'AIP paid', 'right')}
                </>}
                {sortTh('contacted', 'Contacted')}
                {sortTh('openclaim', 'Claim')}
                {sortTh('status', 'Status')}
                {sortTh('flag', 'Flag', 'center')}
              </tr>
            </thead>
            <tbody>
              {view.map((r, idx) => (
                <tr key={r.farmer.id} onClick={() => setSelected(sel => sel === r.farmer.id ? null : r.farmer.id)}
                  style={{ cursor: 'pointer', background: selected === r.farmer.id ? '#f2f9f2' : r.flag ? '#fdf5f2' : (idx % 2 ? '#fafcfa' : '#fff') }}>
                  <td style={{ ...td, fontWeight: 700, color: '#1a2e1a', minWidth: 160 }}>{r.farmer.name}</td>
                  {ct.kind === 'harvest' ? <>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{guarCell(r)}</td>
                    <td style={{ ...td, textAlign: 'right', borderLeft: '1px solid #eef3ee' }}>{measuredCell(r)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{jdCell(r)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{apprCell(r)}</td>
                  </> : <>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{bu(r.pp.maxAc)} ac</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{r.pp.reportedAc > 0.005 ? bu(r.pp.reportedAc) + ' ac' : '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{ppExpectCell(r)}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{money(r.pp.paid)}</td>
                  </>}
                  <td style={{ ...td, borderLeft: '1px solid #eef3ee' }}>{contactedBox(r)}</td>
                  <td style={td}>{claimBadge(r)}</td>
                  <td style={td}>{statusSel(r)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{r.flag ? <span title="Under guarantee, no open claim" style={{ fontSize: 16 }}>🚩</span> : (rowHandled(r) ? <span title="Handled — over / claim open / resolved" style={{ color: '#2d6a2d', fontWeight: 800 }}>✓</span> : '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {ct.kind === 'harvest' && rows.length > 0 && <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 8, lineHeight: 1.55 }}><strong>Guarantee</strong> = the {campaignUnit === 'bu' ? 'bushels' : campaignUnit === 'cwt' ? 'hundredweight' : campaignUnit === 'lbs' ? 'pounds' : 'tons'} they must beat (APH × coverage){campaignBasis === 'yield' ? ' — this crop is on a yield plan (APH/YP), so the guarantee is in production and there is no revenue guarantee beside it' : campaignBasis === 'mixed' ? ' — this crop carries both revenue and yield plans in the book, so the revenue comparison is withheld' : ''}. <strong>Production</strong> = tickets + on-farm + appraisal (the tag under it says which); the <span style={{ fontWeight: 800, color: '#b3300f' }}>UNDER</span>/<span style={{ fontWeight: 800, color: '#2d6a2d' }}>OVER</span> pill is its % of guarantee (red = a claim likely). <strong>JD Monitor</strong> = yield-monitor estimate, reference only (<span style={{ color: '#b3300f' }}>⚠ only N% of acres</span> = a partial reading). <strong>Appraised</strong> = the adjuster's figure on released acres, a floor, never a shortfall. Click a row for the county breakdown.</div>}

      {/* Drill-in detail — modal overlay so it pops up in view no matter where the
          clicked row was; closing returns you to the same scroll spot in the roster. */}
      {selectedRow && (
        <div onClick={() => setSelected(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,32,20,0.5)', zIndex: 6000, overflowY: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: isMobile ? '10px' : '28px' }}>
          <div onClick={e => e.stopPropagation()}
            /* ⚠ `minWidth: 0` IS WHAT KEEPS THIS MODAL INSIDE THE PHONE.
             *
             * The panel is a FLEX ITEM of the backdrop, and a flex item defaults to
             * `min-width: auto` — "never shrink below your widest content". So any wide
             * descendant (the eight-column county table, the 61-row appraisal table)
             * sets the panel's floor, the panel grows past the 390px viewport, and the
             * backdrop — `overflowY: auto`, no overflowX — CLIPS the right-hand side.
             * Not scrolls: clips.
             *
             * Kevin's screenshots, 2026-10-06: "WHERE THIS CLAIM STANDS" with no
             * Guarantee and no Production value on screen at all, the county table cut
             * mid-header at "bu/a", the appraisal table cut at its bushels column.
             * `width: 100%` was already correct and could not take effect.
             *
             * ⚠ THIS REPO HAS WRITTEN THE SAME RULE DOWN TWICE ALREADY — on the grid
             * item that holds ProductionEvidencePanel ("a grid item defaults to
             * min-width: auto … the right-hand values were CLIPPED, not scrollable")
             * and on the sticky header's farm name ("min-width: 0 is load-bearing — a
             * flex child will not ellipsize without it"). Both fixed one box INSIDE
             * this panel, so the clipping simply moved out a level. Fixing the
             * container is what makes those two fixes reachable.
             *
             * The inner tables keep their own `overflowX: auto` wrappers, which is
             * where a genuinely wide table is supposed to scroll — on its own, rather
             * than by dragging the page.
             */
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 920, minWidth: 0, margin: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.3)' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderRadius: '12px 12px 0 0', borderBottom: '1px solid #e4ece4', padding: isMobile ? '12px 14px' : '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* ⚠ THE FARM AND THE CLAIM ARE TWO LINES, AND THE NAME TRUNCATES. They used to
                  be ONE line inside a 63px sticky box with `overflow: visible`, so at phone
                  width any name that wrapped left its second line spilling out — Kevin's
                  screenshot of this showed a sticky bar reading just "Harvest" with content
                  scrolling past it. It is not random: it happens to every farm whose name
                  wraps, and most of them do at 390px. `min-width: 0` is load-bearing — a flex
                  child will not ellipsize without it, it just pushes the Close button off. */}
              <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                <div style={{ fontFamily: "'Lora',serif", fontSize: isMobile ? 15 : 18, fontWeight: 800, color: '#1a2e1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedRow.farmer.name}</div>
                <div style={{ color: '#60735f', fontWeight: 600, fontSize: 11.5, marginTop: 1 }}>{ct.label} · CY{year}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ border: 'none', background: '#eef3ee', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontWeight: 700, color: '#4a5a4a', whiteSpace: 'nowrap' }}>✕ Close</button>
            </div>
            <div style={{ padding: isMobile ? 14 : 20 }}>
              {/* Notes (shared by all claim types) */}
              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Notes</div>
                <textarea defaultValue={selectedRow.tracking?.notes || ''} onBlur={e => saveTracking(selectedRow.farmer.id, { notes: e.target.value || null })}
                  placeholder="Call notes, what they reported, next step…" rows={2}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cdddcd', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              {ct.kind === 'harvest'
                ? (<>
                    {/* ⚠ THE EVIDENCE PANEL IS MOUNTED INSIDE ClaimCheckView NOW, at the top of
                        its grid, because the read it leads with needs the guarantee from that
                        component and the source and acres from this one. It used to render here,
                        AFTER the whole claim screen, with a note explaining that the claim
                        question sat above it and the reporting question below. That separation
                        is preserved — "Where this claim stands" is the claim question and
                        "What we hold for a production report" is the reporting one — but a
                        reader reaches the answer first instead of eight screens down. */}
                    <ClaimCheckView farmers={farmers} isMobile={isMobile} lockFarmerId={selected} lockCrop={crop} lockYear={year} jdData={selectedRow.jd} />
                  </>)
                : (
                  <div style={{ fontSize: 13, color: '#4a5a4a', lineHeight: 1.6 }}>
                    <div><strong>Prevent-plant eligible:</strong> {bu(selectedRow.pp.maxAc)} ac across {selectedRow.pp.counties.join(', ') || '—'}{selectedRow.pp.plantedAc > 0 ? ` · ${bu(selectedRow.pp.plantedAc)} ac planted` : ''}</div>
                    <div><strong>Prevent-plant acres reported:</strong> {bu(selectedRow.pp.reportedAc)} ac</div>
                    {selectedRow.pp.expected != null && (
                      <div><strong>Adjuster expects:</strong> {ppExpectCell(selectedRow)} · <strong>AIP paid:</strong> {money(selectedRow.pp.paid)}</div>
                    )}
                    {selectedRow.pp.setAside?.length > 0 && (
                      // What was EXCLUDED and why. A narrowed number that just
                      // appears is indistinguishable from a wrong one, and this
                      // is the figure a PP claim gets filed against — so the
                      // superseded policy's acres are named, not silently gone.
                      <div style={{ marginTop: 6, color: '#a4700f', fontSize: 12 }}>
                        Excluded {selectedRow.pp.setAside.length} eligibility row{selectedRow.pp.setAside.length === 1 ? '' : 's'} from a policy the AIP file no longer carries, superseded by a live policy on the same crop and county: {selectedRow.pp.setAside.map((x, i) => <span key={i}>{i > 0 ? '; ' : ''}{x.cell.split('|').reverse().filter(Boolean).join(' ')} — {bu(x.acres)} ac</span>)}.
                      </div>
                    )}
                    <div style={{ marginTop: 6, color: '#60735f', fontSize: 12 }}>Prevent-plant is acreage-based — mark Reported / Status from the conversation and file if they had prevented acres. (No scale-ticket production check applies.)</div>
                    {/* The determination itself — eligibility, the roll, the working, and
                        what it could not source. Everything above this line is the CARRIER's
                        own figures; everything below is ours. */}
                    <AdjusterPanel farmer={selectedRow.farmer} cropYear={year} isMobile={isMobile} />
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CLAIM CROSS-CHECK — the carrier's own worksheet, against itself.
//
// Kevin, 2026-09-23: *"The reason for this tool is we have had AIPs make mistakes and we
// want to be able to catch those… All the cross check needs to do is identify a potential
// discrepancy and then we take it from there."*
//
// ⚠ IT FLAGS; IT DOES NOT ADJUDICATE. Both figures, both sources, the word "discrepancy" —
// never "underpaid", which is a conclusion this tool has no standing to reach. The rules
// live in src/claims/claimCrossCheck.js so the screen cannot grow a second opinion.
//
// ⚠ AND IT READS m13_loss_detail, WHICH NOTHING HAS EVER READ. 4,173 rows of the AIP's own
// P21 claim worksheet, mirrored since August and consumed by zero lines of app code. That
// is why this needs no new feed and no waiting: the carrier already tells us, on its own
// record, what it guaranteed and what it paid.
// ─────────────────────────────────────────────────────────────────────────────
// The stage codes an agent thinks in. PF/P2/P are the prevent-plant family and H/UH the
// harvest one — UH is the production-to-count offset against a gross H and nets into the
// same cheque, which is why they fold together here exactly as CLAIM_STAGE_GROUP folds
// them on the claims page. A code with no established meaning renders RAW and filters as
// itself: this file's standing rule for an unsourced code, and the reason the list is
// built from the DATA rather than from a hardcoded array.
const CC_STAGE_GROUPS = Object.freeze({ PF: "Prevent plant", P2: "Prevent plant", P: "Prevent plant",
                                        H: "Harvest", UH: "Harvest", R: "Replant" });
const ccStageLabel = (code) => CC_STAGE_GROUPS[String(code || "").toUpperCase()] || (code ? String(code).toUpperCase() : "not stated");

function ClaimCrossCheckPanel({ isMobile, restrictFarmerIds = null }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [year, setYear] = useState("all");
  const [open, setOpen] = useState({});
  const [est, setEst] = useState(null);
  const [whose, setWhose] = useState(null);
  // The expected-vs-paid book (Kevin, 2026-09-24). Fetched separately from the
  // carrier-vs-itself worklist so a failure in either half cannot blank the other.
  const [cov, setCov] = useState(null);
  const [covErr, setCovErr] = useState(null);
  const [openCell, setOpenCell] = useState(null);   // the derivation popup
  const [openFarm, setOpenFarm] = useState({});
  // The lenses. All default to "all" — a page that opens pre-filtered hides work.
  const [fCounty, setFCounty] = useState("all");
  const [fCrop, setFCrop] = useState("all");
  const [fStage, setFStage] = useState("all");
  const [fAip, setFAip] = useState("all");

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        // ⚠ ORDER BY THE PRIMARY KEY — THIS READ PAGES, AND `crop_year` IS NOT UNIQUE.
        // `m13_loss_detail` is 4,173 rows against a 1,000-row page, so this spans five
        // pages, and Range paging on a non-unique order DROPS AND REPEATS rows across
        // page edges. Both halves are silent: the row COUNT still came back 4,173, so
        // nothing looked wrong. Measured against the live worksheet, the page reported
        // 44 discrepancies and $465,543 where the true figures are 40 and $440,570 —
        // four invented findings and ~$25,000 of invented "unexplained" money, on the
        // one tool whose whole purpose is catching a carrier's mistakes. It is also
        // non-deterministic, because it depends on the order Postgres happens to return
        // rows that share a crop year.
        // The display order is applied in JS below; the FETCH order only has to be unique.
        const r = await sbFetch("m13_loss_detail", { select: "*", order: "id.asc" });
        if (!dead) setRows(r || []);
      } catch (e) { if (!dead) setErr(e.message || String(e)); }
    })();
    return () => { dead = true; };
  }, []);

  // ── WHOSE CLAIM IS IT (Kevin, 2026-09-24: "needs farm names not just claim numbers"
  //    and then "we need to figure out the claim number situation") ──
  //
  // The carrier's worksheet names nobody: `m13_loss_detail` carries `producer_key` (its
  // insured key) and `iif_key` (its coverage key) and no name at all. So the farm is
  // JOINED, and the join is keyed on the coverage — never on the claim number.
  //
  // ⚠ `indemnities.cid` IS COMMA-JOINED, exactly like `.crop` and `.county`. This file
  // already records that those two are LABELS and never keys, and `cid` is the same
  // column shape: NAU CY2025 claim 00000036 stores
  //   cid = "844972421,844972422,844972424,844972428,844972429,844972430,844972431"
  // — seven of STOKOE 1812 INC's coverage keys in one field. Used whole it matches no
  // `iif_key` at all, which is why the first cut of this join fell back to the claim
  // number and reached 92%. SPLIT, each id in that list is an `iif_key`, and it pairs
  // the carrier's own coverage to the policy the money was paid on.
  //
  // Measured over the live worksheet 2026-09-24, 454 claims:
  //   A  the claim's own coverage keys, via the split cid   447   (98.5%)
  //   B  the claim number happens to name exactly one farm    4
  //   C  the GROUND — m13_land CLUs against policy_fields     2
  //   -  nobody: no indemnity row, no CLU we hold             1
  //
  // ⚠ AND IT IS EXACT, NOT A TIE-BREAK: ZERO claims have coverage keys naming more than
  // one farm. The old claim-number join could not say that — it had to show every name
  // it found and badge the row "2 farms", because 29 claim numbers carry two different
  // insureds. Those are now two separate claims (see crossCheckBook), each with one farm.
  //
  // Rung C is the ground itself and is deliberately LAST and LAZY: only the handful the
  // first two rungs cannot answer, two small targeted queries. It earns its place on the
  // largest finding on the board — CY2025 claim 00000183 has no `indemnities` row at all,
  // and 17 of its CLUs sit on MP-0935914, so it reads FIRE CREEK FARMS instead of blank.
  // It refuses unless every CLU that matches names ONE farm.
  //
  // A claim that still resolves to nobody keeps its number and says "farm not on file".
  // One does: NAU CY2025 00000002, a PA/Somerset grower whose CY2025 policy row this
  // book does not hold (Somerset starts at CY2026) and whose CLUs are in no
  // `policy_fields` row. Nothing we have names them, so nothing is claimed.
  useEffect(() => {
    if (!rows) return;
    let dead = false;
    (async () => {
      try {
        const [ind, pol, far] = await Promise.all([
          sbFetch("indemnities", { select: "aip,crop_year,claim_number,cid,policy_id", order: "id.asc" }),
          sbFetch("policies", { select: "id,farmer_id", order: "id.asc" }),
          sbFetch("farmers", { select: "id,name", order: "id.asc" }),
        ]);
        const name = new Map((far || []).map((f) => [f.id, f.name]));
        const owner = new Map((pol || []).map((p) => [p.id, p.farmer_id]));

        // iif_key -> farmer, out of each indemnity row's own cid LIST
        const byIif = new Map();
        const byNumber = new Map();
        for (const i of ind || []) {
          const fid = i.policy_id && owner.get(i.policy_id);
          if (!fid) continue;
          for (const c of String(i.cid == null ? "" : i.cid).split(/[^0-9]+/)) {
            if (c) byIif.set(c, fid);
          }
          const nk = [i.aip, i.crop_year, i.claim_number].join("|");
          if (!byNumber.has(nk)) byNumber.set(nk, new Set());
          byNumber.get(nk).add(fid);
        }

        // The claims themselves, keyed EXACTLY the way crossCheckBook keys them — this
        // coercion is the module's own `num`, copied because a key built two ways is a
        // key that matches nothing the day one side rounds differently.
        const ccNum = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
        const claims = new Map();
        for (const r of rows) {
          const k = [r.aip, ccNum(r.crop_year), r.claim_number,
            r.producer_key == null ? null : String(r.producer_key)].join("|");
          if (!claims.has(k)) claims.set(k, { nk: [r.aip, r.crop_year, r.claim_number].join("|"), iifs: new Set(), producer: r.producer_key });
          if (r.iif_key) claims.get(k).iifs.add(String(r.iif_key));
        }

        const m = new Map();
        const stillOpen = [];
        for (const [k, c] of claims) {
          const fids = new Set();
          for (const i of c.iifs) { const f = byIif.get(i); if (f) fids.add(f); }
          if (fids.size === 1) { m.set(k, { farmerIds: fids, how: "coverage" }); continue; }
          if (!fids.size) {
            const set = byNumber.get(c.nk);
            if (set && set.size === 1) { m.set(k, { farmerIds: new Set(set), how: "number" }); continue; }
          }
          // fids.size > 1 would be a claim whose coverages name two farms — measured zero,
          // and if it ever happens every name is kept rather than one being picked.
          if (fids.size > 1) { m.set(k, { farmerIds: fids, how: "coverage" }); continue; }
          if (c.producer) stillOpen.push({ k, producer: String(c.producer) });
        }

        // Rung C — the GROUND. Only for what is left, and only when it is unanimous.
        if (stillOpen.length && stillOpen.length <= 40) {
          try {
            const land = await sbFetch("m13_land", {
              select: "producer_key,clu_id",
              filters: [`producer_key=in.(${stillOpen.map((s) => s.producer).join(",")})`],
              order: "id.asc",
            });
            const cluByProd = new Map();
            for (const l of land || []) {
              if (!l.clu_id) continue;
              const p = String(l.producer_key);
              if (!cluByProd.has(p)) cluByProd.set(p, new Set());
              cluByProd.get(p).add(l.clu_id);
            }
            const wanted = [...new Set([].concat(...[...cluByProd.values()].map((s) => [...s])))];
            const cluFarm = new Map();
            for (let i = 0; i < wanted.length; i += 100) {
              const chunk = wanted.slice(i, i + 100);
              const pf = await sbFetch("policy_fields", {
                select: "clu_id,farmer_id",
                filters: [`clu_id=in.(${chunk.join(",")})`],
                order: "id.asc",
              });
              for (const r of pf || []) {
                if (!r.clu_id || !r.farmer_id) continue;
                if (!cluFarm.has(r.clu_id)) cluFarm.set(r.clu_id, new Set());
                cluFarm.get(r.clu_id).add(r.farmer_id);
              }
            }
            for (const s of stillOpen) {
              const fids = new Set();
              for (const c of cluByProd.get(s.producer) || []) for (const f of cluFarm.get(c) || []) fids.add(f);
              // Unanimous or nothing: ground changes hands, and a split vote names nobody.
              if (fids.size === 1) m.set(s.k, { farmerIds: fids, how: "ground" });
            }
          } catch { /* the ground is a bonus rung; losing it costs a name, never a number */ }
        }

        for (const v of m.values()) {
          v.names = new Set([...v.farmerIds].map((f) => name.get(f)).filter(Boolean));
        }
        if (!dead) setWhose(m);
      } catch { if (!dead) setWhose(new Map()); }
    })();
    return () => { dead = true; };
  }, [rows]);

  // ── WHAT THE CARRIER SHOULD HAVE PAID, AND WHAT IT DID ────────────────────
  // The coverage side of the page. `crop_coverage_units` is where the AIP states the
  // prevent-plant guarantee per unit, which IS the expected figure — see
  // src/claims/claimExpected.js. Read here rather than server-side so the rule stays one
  // shared module the checker can drive directly.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [coverages, units, pol, far, ind] = await Promise.all([
          // ⚠⚠ `insurance_options`, `practice_code` AND `mpci_plan_code` ARE READ BY
          // `modelInputs` BELOW AND WERE NEVER SELECTED — so "We expect" was blank on
          // EVERY farm row in the book.
          //
          // The chain: `modelInputs` passes `options: c.insurance_options` and
          // `optionsCaptured: !!(c.insurance_options && ...)` into `resolvePpFactor`.
          // An unselected column is `undefined`, so `optionsCaptured` was always
          // FALSE, the factor always came back as a BAND (corn 0.55–0.65, soybeans
          // 0.60–0.70) rather than a point, `modelled.exact` was never true, and the
          // farm rollup — which requires `exact` — had nothing to total. The band was
          // computed correctly and shown one level down on the expanded cell rows,
          // which is why the page looked alive with an empty column.
          //
          // Measured over the live CY2026 book: options are captured on 108 of 108
          // base coverages that report prevent-plant acres, and resolving the factor
          // off the BASE coverage returns an EXACT figure on 105 of 105 cells.
          // Nothing was missing from the data; the query simply never asked for it.
          //
          // This is `check-query-contract`'s class — a rule reading a field its own
          // query never SELECTED, where the guard is dead and every test still passes
          // — and that checker scans `api/` only, so nothing covered this.
          sbFetch("crop_coverage", { select: "iif_key,policy_id,county,crop,acres,pp_acres,liability,"
            + "practice_code,insurance_options,mpci_plan_code,price_election_pct,type_code",
            filters: ["iif_key=not.is.null"], order: "id.asc" }),
          sbFetch("crop_coverage_units", { select: "policy_id,crop_year,county,crop,unit_number,sub_unit,"
            + "is_prevent_plant,acres,liability,aph_yield,coverage_level,price_election,share", order: "id.asc" }),
          // ⚠ `state` TOO — `modelInputs` resolves the prevent-plant factor per
          // (crop, state, county, practice) and reads it off the POLICY. Unselected
          // it is undefined, and a factor table asked for no state answers for no
          // county. Same query contract as the coverage select above.
          sbFetch("policies", { select: "id,farmer_id,aip,policy_number,crop_year,state", order: "id.asc" }),
          sbFetch("farmers", { select: "id,name", order: "id.asc" }),
          sbFetch("indemnities", { select: "policy_id,status,crop_year", order: "id.asc" }),
        ]);
        // ⚠ A FARM STILL IN ADJUSTMENT IS NOT A FARM THAT WAS UNDERPAID. Measured
        // 2026-09-24: 11 of the 14 unpaid prevent-plant cells belong to farms with a claim
        // still open, and listing them would make the work list mostly noise.
        const polFarm = new Map((pol || []).map((p) => [p.id, p.farmer_id]));
        const openByFarmer = new Map();
        for (const i of ind || []) {
          const f = polFarm.get(i.policy_id);
          if (f && /open|adjust|pending/i.test(String(i.status || ""))) openByFarmer.set(f, true);
        }
        if (!dead) setCov({ coverages: coverages || [], units: units || [], policies: pol || [], farmers: far || [], openByFarmer });
      } catch (e) { if (!dead) setCovErr(e.message || String(e)); }
    })();
    return () => { dead = true; };
  }, []);

  // The frozen estimates. Read separately from the worksheet so a missing sql/074 cannot
  // take the carrier-vs-itself half of the page down with it.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await adminApiFetch("/api/market?fn=claim-estimate&op=list");
        const j = await apiJson(r, "the estimate service");
        if (!dead) setEst(j);
      } catch { if (!dead) setEst({ estimates: [], available: false, reason: "could not be read" }); }
    })();
    return () => { dead = true; };
  }, []);

  // A worksheet line names its coverage by `iif_key` and carries no crop or county of its
  // own, so those two lenses are resolved through the coverage the carrier keyed it to —
  // the same join the farm name already uses.
  const iifMeta = useMemo(() => {
    const m = new Map();
    for (const c of (cov && cov.coverages) || []) {
      if (c.iif_key == null) continue;
      m.set(String(c.iif_key), { crop: (c.crop || "").toUpperCase(), county: (c.county || "").toUpperCase() });
    }
    return m;
  }, [cov]);

  // ⚠ FILTERED AT SOURCE, SO EVERY TOTAL ON THE PAGE NARROWS WITH IT. Filtering the
  // rendered rows instead would leave "141 cells · 91 agree" describing a book the reader
  // is no longer looking at — the shape this codebase already fixed once on the
  // outstanding-premium tab, where a shortened list kept whole-farm balances beside it.
  const rowMatches = useMemo(() => (r) => {
    if (fAip !== "all" && r.aip !== fAip) return false;
    if (fStage !== "all" && ccStageLabel(r.stage_code) !== fStage) return false;
    if (fCrop !== "all" || fCounty !== "all") {
      const meta = iifMeta.get(String(r.iif_key));
      // A line whose coverage we cannot resolve is DROPPED by a crop/county lens rather
      // than kept: it cannot be shown to satisfy a filter it was never tested against.
      if (!meta) return false;
      if (fCrop !== "all" && meta.crop !== fCrop) return false;
      if (fCounty !== "all" && meta.county !== fCounty) return false;
    }
    return true;
  }, [fAip, fStage, fCrop, fCounty, iifMeta]);

  const fRows = useMemo(() => (rows || []).filter(rowMatches), [rows, rowMatches]);
  const anyFilter = fCounty !== "all" || fCrop !== "all" || fStage !== "all" || fAip !== "all";
  // The expected book reads coverages directly, so it takes the same crop/county lens.
  // Stage and carrier do not apply to a COVERAGE cell and are deliberately not passed:
  // a cell is not a stage, and filtering it by one would silently empty the book.
  const fCoverages = useMemo(() => {
    const all = (cov && cov.coverages) || [];
    if (fCrop === "all" && fCounty === "all") return all;
    return all.filter((c) => (fCrop === "all" || (c.crop || "").toUpperCase() === fCrop)
                          && (fCounty === "all" || (c.county || "").toUpperCase() === fCounty));
  }, [cov, fCrop, fCounty]);

  // ⚠ `iifMeta` ALREADY RESOLVES iif_key -> { crop, county } and is declared above for the
  // crop/county lenses. A first cut of this added a SECOND map over the same rows, which is
  // the duplicate-rule shape this codebase keeps paying for — two resolvers drift, and then
  // the lens that FILTERS a line and the label that NAMES it disagree about what crop it is.
  // One map, both jobs.
  const book = useMemo(() => {
    if (!rows) return null;
    return crossCheckBook(fRows, { cropYear: year === "all" ? null : +year, covByIif: iifMeta });
  }, [rows, fRows, year, iifMeta]);
  // ⚠ EVERY LIST IS BUILT FROM THE DATA, never a hardcoded array — the same rule the
  // Coverage Book Scan's option list follows, so a stage or a county the carrier starts
  // sending appears on its own instead of being silently unfilterable. Each list is built
  // from the rows the OTHER lenses leave, so a combination that can only ever be empty is
  // not offered in the first place.
  const opts = useMemo(() => {
    const all = rows || [];
    const inYear = (r) => year === "all" || +r.crop_year === +year;
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();
    const metaOf = (r) => iifMeta.get(String(r.iif_key));
    return {
      counties: uniq(all.filter((r) => inYear(r) && (fCrop === "all" || (metaOf(r) || {}).crop === fCrop)
                                    && (fAip === "all" || r.aip === fAip))
                        .map((r) => (metaOf(r) || {}).county)),
      crops: uniq(all.filter((r) => inYear(r) && (fCounty === "all" || (metaOf(r) || {}).county === fCounty)
                                 && (fAip === "all" || r.aip === fAip))
                     .map((r) => (metaOf(r) || {}).crop)),
      stages: uniq(all.filter(inYear).map((r) => ccStageLabel(r.stage_code))),
      aips: uniq(all.filter(inYear).map((r) => r.aip)),
    };
  }, [rows, year, iifMeta, fCrop, fCounty, fAip]);

  const years = useMemo(() =>
    [...new Set((rows || []).map((r) => +r.crop_year).filter(Boolean))].sort((a, b) => b - a), [rows]);

  // ⚠ THE EXPECTED BOOK NEEDS A CROP YEAR. Prevent-plant guarantees are per crop year and
  // `crop_coverage_units` spans several, so an "all years" view would compare one year's
  // payments against every year's stated guarantee. It reports that rather than guessing.
  const expectedYear = year === "all" ? (years[0] || null) : +year;
  const xbook = useMemo(() => {
    if (!rows || !cov || !expectedYear) return null;
    return buildExpectedBook({
      lossLines: fRows, coverages: fCoverages, units: cov.units,
      policies: cov.policies, farmers: cov.farmers, openByFarmer: cov.openByFarmer,
      cropYear: expectedYear,
      // ── OUR OWN NUMBER, FROM RMA'S INPUTS ────────────────────────────────
      // Kevin, 2026-09-28: *"Using the AIPs number is not a cross check… I want us to
      // calculate what we expect their claim to be and then compare it against what
      // actually gets paid."* Supplied HERE rather than inside claimExpected.js because
      // App.jsx owns both resolvers; a second copy of either is how two engines come to
      // disagree about one farm.
      modelInputs: (c, u) => {
        // ⚠ THE PRICE IS RMA'S PUBLISHED PROJECTED PRICE, AND THE AIP FILE CARRIES NONE.
        // `crop_coverage_units.price_election` is populated on 1 of 177 CY2026 PP units —
        // the dollar price is simply not in the M13 feed — so this comes from our own ADM
        // pull, which is exactly what makes the comparison independent.
        // ⚠ THE PRICE IS ASKED FOR AT THIS UNIT'S OWN PRACTICE FIRST. The
        // collapsed `_rmaFeed` is keyed by crop alone, and six CY2026 NY buckets
        // disagree with themselves — POTATOES holds $17.85 on 36 rows and $33.60
        // on 14, and the last one won. MARQUART's potato units are practice 997,
        // which the ADM prices at $17.85, and the carrier's own liability
        // back-solves to exactly that; the page was quoting $33.60 and reading
        // 1.882x on AARON H MARQUART and THOMAS ADAM MARQUART.
        // `resolveRmaPrice` stays the fallback, so a crop with no practice-level
        // row resolves exactly as it always did.
        const exact = rmaFeedPriceFor(c.crop, expectedYear, "projected", c.practice_code, c.type_code);
        const px = exact != null
          ? { price: exact, source: "rma_adm_practice" }
          : resolveRmaPrice(c.crop, expectedYear, "projected");
        // ⚠ AND THE FACTOR MUST NOT BE THE ONE READ BACK OUT OF THEIR GUARANTEE.
        // `resolvePpFactor` ranks the AIP's own `acre_guarantee_qty` FIRST, which is
        // liability rearranged — the best answer to "what does this policy carry" and the
        // worst to "did they compute it right". `acreGuaranteeQty` is deliberately NOT
        // passed, so the resolver falls to RMA's published table and the elected option.
        // Measured 2026-09-28: options are captured on 85 of 85 prevent-plant coverages
        // and exactly ONE carries PF, so this returns an EXACT factor almost everywhere.
        const pol = (cov.policies || []).find((p) => p.id === c.policy_id) || {};
        const f = resolvePpFactor({
          crop: c.crop, cropYear: expectedYear, state: pol.state, county: c.county,
          practiceCode: c.practice_code, options: c.insurance_options,
          optionsCaptured: !!(c.insurance_options && String(c.insurance_options).trim()),
        });
        return {
          projectedPrice: px && px.price != null ? px.price : null,
          priceBasis: px && px.source ? px.source : null,
          ppFactorLow: f.low, ppFactorHigh: f.high,
          factorExact: f.exact === true, factorBasis: f.basis,
          // ⚠ A CAT LINE PAYS AT 55% OF THE PRICE. The election is on the
          // COVERAGE — `crop_coverage_units.price_election` is null across the
          // book — and without it a CAT cell is modelled at full price and
          // reads as a shortfall the carrier does not owe. Measured on MARQUART
          // BROS' potatoes: ours/theirs 1.818, which is exactly 1/0.55, while
          // every other Marquart cell tied to the dollar. 102 of 2,616 coverage
          // rows carry a reduced election. An absent value means 100%.
          priceElectionPct: c.price_election_pct != null ? +c.price_election_pct : null,
          // ⚠ AND THE TYPE, because `resolveRmaPrice` keys on CROP ALONE. Corn
          // grain (016) is bu/ac at $4.62 and corn silage (026) is ton/ac at a
          // different number entirely; CY2026 carries 6 silage prevent-plant
          // units. Handing the engine the type lets it REFUSE a cell it would
          // otherwise price against the wrong commodity — it does not resolve a
          // per-type price, which is the follow-up.
          crop: c.crop,
          typeCode: c.type_code != null ? String(c.type_code) : null,
        };
      },
    });
  }, [rows, fRows, fCoverages, cov, expectedYear]);
  const xFarms = useMemo(() => {
    if (!xbook) return [];
    if (!restrictFarmerIds || !restrictFarmerIds.length) return xbook.farms;
    return xbook.farms.filter((f) => f.farmerId && restrictFarmerIds.includes(f.farmerId));
  }, [xbook, restrictFarmerIds]);

  // ── READING THE CROSS-CHECK (Kevin, 2026-09-29) ─────────────────────────────
  // *"this claims cross check is still quite confusing, I like the columns but I still
  // dont know I understand what im looking at."*
  //
  // ⚠ THE NUMBERS RECONCILE AND THE PAGE NEVER SHOWED THAT THEY DO. Four lanes partition
  // the cells exactly — driven through the shipped builder over CY2026: 141 = 91 agree +
  // 2 to look at + 11 pending + 37 not estimated. The header named the first two, the grey
  // prose underneath held the other two, and a THIRD figure in that same prose (10
  // superseded worksheet LINES, $23,505) is a different unit entirely. A reader who tries
  // to make the page add up gets 151 and concludes they have misread something. They
  // haven't; the 10 does not belong in that sum.
  //
  // So the partition is shown as a partition — every lane on one line, each a filter —
  // and the superseded note is a footnote, in its own sentence, named as lines.
  const xLanes = useMemo(() => {
    const cells = xFarms.flatMap((f) => f.cells);
    // ⚠ ONE LANE PER CELL, DECIDED BY A LADDER — so the strip's arithmetic is a PROPERTY
    // of the classifier and not a coincidence of today's data. The first cut wrote five
    // independent predicates and derived `other` as the residual; they happened to
    // partition CY2026 (91+2+11+0+37 = 141) and nothing made them. A cell that is both
    // actionable and unpaid would have been counted twice and the strip would have read
    // 142 = 141 with no way to see which segment was wrong.
    //
    // `actionable` is FIRST because it is the question the page exists to answer: a cell
    // somebody must look at is that, whatever else is also true of it.
    const laneOf = (c) => c.actionable ? "look"
      : c.lane === CX_LANE.AGREES ? "agree"
      // Every unpaid cell in CY2026 sits on a farm with a claim still open (11 of 11),
      // but the flag is READ rather than assumed — an unpaid cell with no open claim is a
      // real finding and must never be filed under "pending".
      : c.lane === CX_LANE.NOTHING_PAID ? (c.farmHasOpenClaim ? "pending" : "unpaid")
      : c.lane === CX_LANE.NOT_ESTIMATED ? "notest"
      : "other";
    const is = {};
    for (const k of Object.keys(CX_LANE_WORD)) is[k] = (c) => laneOf(c) === k;
    const n = {};
    for (const k of Object.keys(CX_LANE_WORD)) n[k] = 0;
    for (const c of cells) n[laneOf(c)]++;
    return { cells: cells.length, n, is, laneOf };
  }, [xFarms]);
  // Which lane the reader has narrowed to, or null for all of them.
  const [xLane, setXLane] = useState(null);
  // The reconciled group is most of the book and collapses by default.
  const [xRestOpen, setXRestOpen] = useState(false);


  const money = (v) => (v == null ? "—" : (v < 0 ? "-$" : "$") + Math.abs(Math.round(v)).toLocaleString());
  const btnS = { padding: "3px 9px", borderRadius: 6, border: "1px solid #cfdccf",
                 background: "#fff", color: "#2d4a2d", fontWeight: 700, fontSize: 11.5, cursor: "pointer" };
  // ⚠ EACH ESTIMATE IS MATCHED TO THE CARRIER LINES OF ITS OWN CELL, and where the carrier
  // has paid NOTHING for that cell the comparison is PENDING rather than a total shortfall —
  // an absent payment is not a zero, and treating it as one would report every open claim as
  // the largest discrepancy on the page.
  const estRows = useMemo(() => {
    if (!est || !est.estimates || !rows) return [];
    return est.estimates.map((row) => {
      // ⚠ MATCHED ON THE COVERAGE (`iif_key`), NOT ON CROP YEAR AND STAGE. Those two alone
      // are the whole book — 257 prevent-plant lines in CY2026 — so every estimate would
      // have been compared against every farm's payments at once. The carrier's worksheet
      // carries `iif_key` on all 392 CY2026 rows, and it is the same key `crop_coverage`
      // carries, so this is the grain a claim is actually adjusted on.
      // An estimate with no coverage key matches NOTHING rather than everything: the
      // comparison then reads PENDING, which is the honest answer for a row we cannot
      // place, and never a confident discrepancy against somebody else's claim.
      const paidLines = !row.iif_key ? [] : rows.filter((r) =>
        Number(r.crop_year) === Number(row.crop_year)
        && String(r.iif_key || "") === String(row.iif_key)
        && ppStageMatches(row.stage_code, r.stage_code));
      return { row, cmp: compareEstimateToPaid(row, paidLines) };
    }).sort((a, b) => Math.abs((b.cmp.diff || 0)) - Math.abs((a.cmp.diff || 0)));
  }, [est, rows]);
  const card = { background: "#fff", border: "1px solid #e3ebe3", borderRadius: 10, padding: 14, marginBottom: 12 };

  if (err) return <div style={{ ...card, color: "#8a3324", fontWeight: 600 }}>Couldn't load the claim worksheet: {err}</div>;
  if (!book) return <div style={{ ...card, color: "#6b7a6b" }}>Reading the carriers' claim worksheets…</div>;

  const t = book.totals;
  // Whose claim, as a display string. Keyed on the CLAIM — which is the insured, not the
  // number — so the two insureds who share NAU CY2025 00000094 are two rows with one farm
  // apiece rather than one row badged "2 farms". A claim nothing can name reads "farm not
  // on file" rather than as a blank the reader has to interpret. Several names can still
  // only happen if a claim's own coverages named two farms (measured: none do); if that
  // ever starts, every name is shown and none is picked.
  const farmOf = (c) => {
    if (!whose) return null;                       // still loading — say nothing yet
    const hit = whose.get(c.key);
    if (!hit || !hit.names || !hit.names.size) return { text: "farm not on file", unknown: true };
    return { text: [...hit.names].join(" + "), unknown: false, several: hit.names.size > 1 };
  };

  // ⚠ `restrictFarmerIds` WAS ACCEPTED AND NEVER READ. The panel is agent-only by its
  // render gate (`mode !== "portal"`), so nothing depended on it — but a prop a component
  // takes and ignores is the same defect as one it never took, and it becomes load-bearing
  // the moment a farm NAME is on the row. Where a caller narrows, the worklist narrows:
  // a claim is kept only when its farm is one of theirs, and a claim whose farm cannot be
  // resolved is DROPPED rather than shown, because an unidentifiable claim is exactly what
  // a narrowed caller must not see.
  const flagged = book.claims.filter((c) => {
    if (c.verdict !== CC_VERDICT.DISCREPANCY) return false;
    if (!restrictFarmerIds || !restrictFarmerIds.length) return true;
    const hit = whose && whose.get(c.key);
    if (!hit || !hit.farmerIds) return false;
    return [...hit.farmerIds].some((id) => restrictFarmerIds.includes(id));
  });

  // ── THE CROSS-CHECK FINDINGS, ATTACHED TO THE FARM THEY BELONG TO ──────────
  // Kevin, 2026-09-28: *"Get rid of this section in claims cross check and have
  // everything display as the columns that are below this: what we expected and what
  // the AIP paid."* The worklist card is gone; these render as rows inside the
  // expected-vs-paid table instead, so the page speaks one language.
  //
  // ⚠⚠ THEIR LEFT-HAND FIGURE IS THE CARRIER'S, NOT OURS, AND THE ROW SAYS SO. The
  // table's own "Expected" column is OUR estimate; a cross-check finding compares the
  // carrier's two figures against each other and the section it replaced said exactly
  // that in its footnote — "not a comparison against any estimate of ours". Putting
  // the carrier's stated guarantee under a heading that means "we expected this" would
  // claim a provenance we do not have, which is the one thing this panel exists not to
  // do. So each row carries `from`, printed beneath it, and the value keeps its own
  // source string off the check.
  //
  // ⚠ AND THE AMOUNTS DO NOT ROLL UP. `f.expected` / `f.paid` are built from `f.cells`
  // and already cover this money from the other direction — the same payment, compared
  // against a different authority. Adding these into the farm total would count one
  // claim twice and inflate every headline on the page. They are DETAIL rows only; the
  // farm's badge says they exist, and its Difference column stays the expected-vs-paid
  // answer it has always been.
  const xcRowsOf = (c) => {
    const out = [];
    for (const l of c.flagged || []) {
      for (const k of (l.checks || [])) {
        if (k.verdict !== CC_VERDICT.DISCREPANCY) continue;
        out.push({
          stage: l.stage, acres: l.acres, label: k.label,
          // The carrier's own stated figure and its own payment, kept as they came.
          theirFigure: k.a ? k.a.value : null,
          paid: k.b ? k.b.value : null,
          diff: k.diff == null ? null : k.diff,
          from: (k.a && k.a.source) || "the carrier's own claim worksheet",
        });
      }
    }
    return out;
  };
  const xcByFarm = new Map();     // farmerId -> [{ claim, rows }]
  const xcUnplaced = [];          // findings no farm could be named for
  for (const c of flagged) {
    const rows = xcRowsOf(c);
    if (!rows.length) continue;
    const hit = whose && whose.get(c.key);
    const ids = hit && hit.farmerIds ? [...hit.farmerIds] : [];
    const entry = { claim: c, rows };
    // A claim naming two farms is shown under BOTH rather than assigned to one — the
    // same rule the worklist followed. Measured: no claim in the book does this today.
    if (!ids.length) xcUnplaced.push(entry);
    else for (const id of ids) {
      if (!xcByFarm.has(id)) xcByFarm.set(id, []);
      xcByFarm.get(id).push(entry);
    }
  }

  // ⚠ THE SCARIEST NUMBERS ON THE PAGE BELONG TO FARMS WITH NOTHING TO DO, and until now
  // they sat in one flat list beside the two that matter. Measured over CY2026: five farms
  // carry |paid − expected| over $1,000 with NO actionable cell — Marquart Bros −$308,131,
  // Thomas Adam Marquart −$228,231, Aaron H Marquart −$189,835, Woznica −$71,323, Marquart
  // Dairy −$32,417 — about $830,000 of red, every dollar of it a claim still in
  // adjustment. The cell rows already said so; the FARM row said "0 match · 5 not
  // compared" next to a six-figure negative, which reads as a shortfall.
  //
  // Grouping is the fix rather than more colour: a heading can say once what a badge on
  // every row cannot, and it puts the two farms that need a person at the top of the page
  // instead of sorted among seventy-seven that do not.
  // ⚠ ONE DEFINITION OF "THIS FARM NEEDS A PERSON", USED BY THE GROUPING AND BY THE ROW.
  // Writing it twice did not merely duplicate it — it BROKE A GUARD. `check-crosscheck-
  // filters` pins `(f.actionable > 0 || xcLines > 0)` to hold the rule that a farm whose
  // only issue is a worksheet contradiction still opens by default; the grouping added a
  // second line matching that same pattern, so the mutation that strips it from the ROW
  // left the checker green against the copy in the grouping. Measured by
  // `mutate-crosscheck-filters` 2026-09-29: 1 missed of 27.
  //
  // Two call sites asking one question is exactly what this repo keeps paying for, and
  // here it cost the guard rather than the behaviour. One rule, both callers.
  const xNeedsLook = (f) => f.actionable > 0
    || (xcByFarm.get(f.farmerId) || []).reduce((n, e) => n + e.rows.length, 0) > 0;
  const xGroups = (() => {
    const keep = (f) => !xLane || f.cells.some(xLanes.is[xLane]);
    const farms = xFarms.filter(keep);
    const look = [], pending = [], rest = [];
    for (const f of farms) {
      // A worksheet contradiction is a different question from our estimate disagreeing,
      // and it still needs a person — so it lands in the same group, counted separately.
      if (xNeedsLook(f)) look.push(f);
      else if (f.cells.some(xLanes.is.pending)) pending.push(f);
      else rest.push(f);
    }
    return { look, pending, rest };
  })();

  return (
    <div>
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#1a2e1a", marginBottom: 4 }}>
          🔍 Claim cross-check
        </div>
        <div style={{ fontSize: 12.5, color: "#5c6f5c", lineHeight: 1.5, marginBottom: 10 }}>
          What the carrier should have paid, against what it did. For prevent plant the expected
          figure is the carrier's <b>own stated guarantee</b> on the unit — not an estimate of ours.
          It flags — it does not decide who is right.
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          {(() => {
            const sel = { padding: "5px 8px", border: "1px solid #cfdccf", borderRadius: 6, background: "#fff", maxWidth: 190 };
            const lab = { fontSize: 12, fontWeight: 700, color: "#2d4a2d", display: "flex", flexDirection: "column", gap: 3 };
            const L = (label, value, setter, list, allText) => (
              <label style={{ ...lab, color: value === "all" ? "#2d4a2d" : "#1f5c2e" }}>
                {label}
                <select value={value} onChange={(e) => setter(e.target.value)}
                        style={{ ...sel, borderColor: value === "all" ? "#cfdccf" : "#2d6a2d",
                                 fontWeight: value === "all" ? 400 : 700 }}>
                  <option value="all">{allText}</option>
                  {list.map((v) => <option key={v} value={v}>{typeof v === "string" ? v.replace(/_/g, " ") : v}</option>)}
                </select>
              </label>
            );
            return (
              <>
                {L("Crop year", year, setYear, years, "all")}
                {L("County", fCounty, setFCounty, opts.counties, "all counties")}
                {L("Crop", fCrop, setFCrop, opts.crops, "all crops")}
                {L("Stage", fStage, setFStage, opts.stages, "all stages")}
                {L("Carrier", fAip, setFAip, opts.aips, "both carriers")}
                {anyFilter && (
                  <button onClick={() => { setFCounty("all"); setFCrop("all"); setFStage("all"); setFAip("all"); }}
                          style={{ padding: "6px 12px", border: "1px solid #cfdccf", borderRadius: 6,
                                   background: "#f2f6f2", color: "#2d4a2d", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                    Clear filters
                  </button>
                )}
              </>
            );
          })()}
        </div>
        {/* ⚠ A FILTERED PAGE SAYS SO, BESIDE THE NUMBERS IT CHANGED. Every figure below —
            cells, agree, to look at, lines, unexplained dollars — is rebuilt from the
            filtered rows, so an unlabelled filter turns the whole page into a quiet
            understatement of the book. */}
        {anyFilter && (
          <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: "#1f5c2e",
                        background: "#eef6ec", border: "1px solid #cfe3cb", borderRadius: 6, padding: "6px 10px" }}>
            Filtered — every figure on this page counts only{" "}
            {[fCounty !== "all" && fCounty, fCrop !== "all" && fCrop, fStage !== "all" && fStage,
              fAip !== "all" && fAip.replace(/_/g, " ")].filter(Boolean).join(" · ")}
            {" "}({fRows.length.toLocaleString()} of {(rows || []).length.toLocaleString()} worksheet lines)
            {(fStage !== "all" || fAip !== "all") && (
              <span style={{ fontWeight: 600, color: "#4a6e4a" }}>
                {" "}· the stage and carrier lenses narrow the worklist only — a coverage cell is not a stage
              </span>
            )}
          </div>
        )}
        {/* ⚠ TWO CROSS-CHECKS ON ONE SCREEN, IN TWO UNITS, AND ONLY ONE SAID SO. This
            block counts WORKSHEET LINES — the carrier's own document against itself — and
            the section below it counts COVERAGE CELLS, our estimate against what was paid.
            Both partition their own total (4,183 = 27 + 3,630 + 526 here; 141 = 91 + 2 +
            11 + 37 below) and neither sums to the other, so a reader who takes them for one
            table can only conclude the page does not add up. Naming the unit in the heading
            is the whole fix; the numerals were already correct. */}
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                      color: "#6b7a6b", marginTop: 14 }}>
          1 · The carrier's worksheet against itself
          <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0, color: "#7c8c7c" }}>
            {" "}— counted in worksheet lines
          </span>
        </div>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 8 }}>
          {/* ⚠ "OPEN QUESTIONS" IS NOT A SECOND NAME FOR "NOT CHECKABLE", AND WITHOUT IT
              THE BIGGEST STAGE IN THE WORKSHEET READS AS VERIFIED. A line's verdict is
              "did ANYTHING agree", so a line can carry an unresolved check and still read
              `agrees` on a different one — which is the normal case for stage FL, 2,045
              of 4,183 lines, where the only comparison that resolves is the carrier's own
              preliminary figure against its own final one (identical on 1,856 of 1,856).
              Counted at the CHECK grain, so nothing hides. Always >= "not checkable". */}
          {[["discrepancies", t.discrepancy, "#8a3324"], ["agree", t.agrees, "#2d6a2d"],
            ["not checkable", t.notCheckable, "#8a6d1f"],
            ["open questions", t.unresolved, "#8a6d1f"], ["lines read", t.lines, "#5c6f5c"]].map(([k, v, c]) => (
            <div key={k}>
              <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{v.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: "#60735f", fontWeight: 600 }}>{k}</div>
            </div>
          ))}
        </div>
        {/* ⚠ REACH IS STATED PER CARRIER, BECAUSE IT DIFFERS COMPLETELY. Measured: NAU states a
            guarantee on every row of its worksheet; R&H states one on none of 410. A single
            headline number would describe neither, and R&H reading "0 discrepancies" without
            this line would read as a clean carrier rather than an unreadable one. */}
        <div style={{ marginTop: 10, fontSize: 12, color: "#33402f" }}>
          {book.byAip.map((a) => (
            <div key={a.aip} style={{ padding: "2px 0" }}>
              <b>{a.aip.replace(/_/g, " ")}</b> — {a.lines.toLocaleString()} lines ·{" "}
              <span style={{ color: a.discrepancy ? "#8a3324" : "#2d6a2d", fontWeight: 700 }}>
                {a.discrepancy} discrepanc{a.discrepancy === 1 ? "y" : "ies"}</span>
              {a.notCheckable === a.lines
                ? <span style={{ color: "#8a6d1f", fontWeight: 600 }}> · none of it checkable — this carrier
                    does not state a guarantee on its claim record, so there is nothing to compare a payment against</span>
                : a.notCheckable > 0 ? <span style={{ color: "#8a6d1f" }}> · {a.notCheckable} not checkable</span> : null}
            </div>
          ))}
        </div>
      </div>

      {/* ══════════ THE WORKLIST LEADS (Kevin, 2026-09-28) ══════════
          "the claims you want me to look at are currently at the bottom of the page,
          those should be at the top if you want me to review them."
          This is the only section that asks for an action. The expected-vs-paid book
          below it is the wider read — 141 cells of which most agree — and a worklist
          under it is a worklist nobody scrolls to. */}
      {/* ══════════ THE WORKLIST CARD IS GONE (Kevin, 2026-09-28) ══════════
          *"Get rid of this section in claims cross check and have everything display as
          the columns that are below this: what we expected and what the AIP paid."*
          It was an expander list of "largest gap ▸" per claim, a second presentation of
          the same money in a different shape. The FINDINGS did not go with it — every
          one now renders as a row inside the table below, in those three columns, under
          the farm it belongs to. See `xcByFarm`.

          What survives here is only the half that has nowhere else to live: the
          all-clear sentence, and the count of questions nothing could settle. */}
      {flagged.length === 0 ? (
        <div style={{ ...card, color: "#2d6a2d", fontWeight: 600 }}>
          {/* ⚠ AN EMPTY TABLE NAMES THE FILTER THAT EMPTIED IT. Otherwise a mistyped lens
              reads as a carrier with nothing to answer for, which is the one conclusion
              this page must never reach by accident. */}
          {anyFilter
            ? <>No line disagrees with its own worksheet <b>under this filter</b>. Clear it to read the whole book.</>
            : <>No line disagrees with its own worksheet in this crop year.</>}
          {/* ⚠ THE COUNT HERE IS `unresolved`, NOT `notCheckable`, AND THE DIFFERENCE IS
              THE WHOLE POINT OF THIS BOX. A green "no line disagrees" panel is the
              strongest assurance this page gives, so the number beside it has to cover
              every line carrying an unanswered question — including the ones whose
              LINE-level verdict is `agrees` because a different check resolved. On the
              live book that is 1,856 FL lines whose only resolved comparison is the
              carrier's preliminary figure against its own final one. Counting only
              `notCheckable` here reports them as verified. */}
          {t.unresolved > 0 && <span style={{ color: "#8a6d1f", fontWeight: 600 }}>
            {" "}{t.unresolved.toLocaleString()} line{t.unresolved === 1 ? "" : "s"} still
            carr{t.unresolved === 1 ? "ies" : "y"} a question nothing in the file could
            settle — see the reach above.</span>}
        </div>
      ) : null}

      {/* ══════════ WHAT THEY SHOULD HAVE PAID (Kevin, 2026-09-24) ══════════
          "The Farm Name, then a drill down with a separate for crop and county line with
          multiple columns showing our estimate, AIP Paid, Difference… I want to see all
          claims on this page but any claims that match exactly can be collapsed, then sort
          the others by biggest dollar discrepancy at the top."
          ⚠ TWO LANES, AND THE FIRST IS THE ONE THAT CATCHES A MISSED COUNTY. A county the
          carrier never paid produces no line to disagree with, so an amount comparison
          cannot see it at all — it is found by asking which coverages carrying a loss have
          NO paid line. Replayed against the data as it stood before NAU re-issued Sullivan
          Farms' claim, MONROE SOYBEANS reads "expected $10,389, paid $0". */}
      {covErr ? (
        <div style={{ ...card, color: "#8a3324", fontWeight: 600 }}>Couldn't load coverage: {covErr}</div>
      ) : !xbook ? (
        <div style={{ ...card, color: "#6b7a6b" }}>Reading the coverages the carriers stated…</div>
      ) : (
        <div style={card}>
          <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
              2 · What they should have paid — CY{expectedYear}
              <span style={{ fontWeight: 600, fontSize: 11, color: "#7c8c7c" }}> — counted in coverage cells</span>
            </div>
            <span style={{ fontSize: 12, color: "#6b7a6b" }}>
              {xbook.totals.cells} coverage cells · <b style={{ color: "#2d6a2d" }}>{xbook.totals.agrees} agree
              to the dollar</b>
              {xbook.totals.actionable > 0 && <> · <b style={{ color: "#8a3324" }}>{xbook.totals.actionable} to
                look at</b></>}
            </span>
          </div>
          {/* ⚠ THE PARTITION, SHOWN AS A PARTITION. Every cell is in exactly one lane and
              the four sum to the total — 141 = 91 + 2 + 11 + 37 on CY2026, driven through
              the shipped builder. The page used to name two of them in the header and bury
              the other two in prose, alongside a THIRD figure in a different unit (10
              superseded worksheet LINES), so a reader adding the page up got 151 and
              concluded they had misread it. Each segment is a filter; the counts are the
              same ones the rows are built from, so the strip cannot drift from the table.
              `other` is 0 today and is rendered anyway when it is not — a lane nobody
              assigned must surface rather than make the arithmetic silently stop working. */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 11.5, color: "#6b7a6b", fontWeight: 700, marginRight: 2 }}>
              {xLanes.cells} coverage cells
            </span>
            <span style={{ color: "#c8d6c8", fontSize: 12 }}>=</span>
            {[["agree", xLanes.n.agree, "to the dollar", "#2d6a2d", "#eaf4e7"],
              ["look", xLanes.n.look, "", "#8a3324", "#fdf0ec"],
              ["pending", xLanes.n.pending, "— claim open", "#8a6d1f", "#fbf5e6"],
              ["unpaid", xLanes.n.unpaid, "— no open claim", "#8a3324", "#fdf0ec"],
              ["notest", xLanes.n.notest, "", "#5c6f5c", "#f0f3f0"],
              ["other", xLanes.n.other, "", "#8a3324", "#fdf0ec"]]
              .filter(([, n]) => n > 0)
              .map(([k, n, label, fg, bg], i) => (
                <React.Fragment key={k}>
                  {i > 0 && <span style={{ color: "#c8d6c8", fontSize: 12 }}>+</span>}
                  <button onClick={() => setXLane(xLane === k ? null : k)}
                    title={xLane === k ? "Show every lane again" : `Show only the farms with a "${CX_LANE_WORD[k]}" cell`}
                    style={{ cursor: "pointer", borderRadius: 7, padding: "3px 9px", fontSize: 11.5, fontWeight: 700,
                             fontVariantNumeric: "tabular-nums", color: fg, background: xLane === k ? fg : bg,
                             border: `1px solid ${xLane === k ? fg : "transparent"}`,
                             ...(xLane === k ? { color: "#fff" } : null) }}>
                    {n} {CX_LANE_WORD[k]}{label ? " " + label : ""}
                  </button>
                </React.Fragment>
              ))}
            {xLane && (
              <button onClick={() => setXLane(null)}
                style={{ cursor: "pointer", border: "none", background: "none", color: "#2d6a2d",
                         fontWeight: 800, fontSize: 11.5, textDecoration: "underline" }}>
                clear
              </button>
            )}
          </div>
          {/* ⚠ WHAT EACH LANE MEANS, AND IT IS NOT DECORATION — "not estimated" is the
              largest lane on the page and a reader who thinks it means "we could not
              check" rather than "there is nothing to check it against" will read the
              whole table as broken. Absent data is NAMED, never omitted. */}
          <div style={{ fontSize: 11.5, color: "#6b7a6b", lineHeight: 1.55, marginBottom: 10 }}>
            {xLanes.n.notest > 0 && <>
              <b>Not estimated</b> is a harvest loss: it pays the guarantee less production to count, and the
              production is a settlement fact this book does not hold — counted, never compared against a
              number we would have had to invent.{" "}
            </>}
            {xLanes.n.pending > 0 && <>
              <b>Pending</b> is a cell the carrier has not paid on a farm whose claim is still in adjustment —
              reported in June and open in September is normal, not an error.{" "}
            </>}
            {xbook.supersededLines > 0 && <>
              Separately, and in a different unit: <b>{xbook.supersededLines} superseded worksheet lines</b>{" "}
              ({money(xbook.supersededDollars)}) were dropped before any of the above was counted. When a
              carrier re-adjusts a claim it re-keys every line and both generations stay in the mirror;
              counting them read as a carrier paying twice. They are LINES, not cells, so they are not part
              of the {xLanes.cells} above.
            </>}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#6b7a6b", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <th style={{ padding: "6px 8px" }}>Farm</th>
                  {/* ⚠ THREE AUTHORITIES, THREE COLUMNS, NEVER MIXED (Kevin, 2026-09-28).
                      "We expect" is OURS — RMA's published price and prevent-plant factor
                      through FCIC-25370 ¶75. "They stated" is the carrier's own liability.
                      "AIP paid" is what arrived. Two differences fall out of that and they
                      catch different failures: ours vs theirs is a WRONG GUARANTEE, theirs
                      vs paid is a WRONG PAYMENT. With two columns only the second was
                      visible, and a wrong liability was inherited by both. */}
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>We expect</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>They stated</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>AIP paid</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>Difference</th>
                  <th style={{ padding: "6px 8px" }} />
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const farmRow = (f) => {
                  // ⚠ COLLAPSED UNLESS THERE IS SOMETHING TO DO. Kevin: "any claims that
                  // match exactly can be collapsed, then sort the others by biggest dollar
                  // discrepancy at the top." The first cut opened any farm that was not
                  // 100% agreeing — and almost every farm carries at least one harvest cell
                  // we do not estimate, so 76 of 79 farms opened and the page became the
                  // whole book unrolled. What decides it is whether the farm has an
                  // ACTIONABLE cell; everything else is one click away.
                  // The carrier's own-worksheet findings for this farm, if any. They open
                  // the farm too: a farm whose only issue is a worksheet contradiction has
                  // no actionable CELL, so without this it would sit collapsed and the
                  // finding would be one click further away than it was before the merge.
                  const xcHere = xcByFarm.get(f.farmerId) || [];
                  const xcLines = xcHere.reduce((s, e) => s + e.rows.length, 0);
                  const shown = openFarm[f.farmerId] != null ? openFarm[f.farmerId]
                              : xNeedsLook(f);
                  const diff = +(f.paid - f.expected).toFixed(2);
                  // OUR total, summed only over the cells we could actually model. A cell
                  // we could not is EXCLUDED and the total is marked partial — adding a
                  // stated figure in to make the column look complete is the conflation
                  // this whole change exists to remove.
                  const ppCells = f.cells.filter((c) => c.expectedDetail && c.expectedDetail.basis === CX_BASIS.PP_STATED);
                  const modCells = ppCells.filter((c) => c.modelled && c.modelled.available && c.modelled.exact);
                  const modTotal = modCells.length ? +modCells.reduce((s, c) => s + c.modelled.expected, 0).toFixed(2) : null;
                  const modPartial = modCells.length > 0 && modCells.length < ppCells.length;
                  // ── ⚠ A BAND IS AN ANSWER; AN EM-DASH IS NOT ────────────────────────
                  // `modTotal` needs every contributing cell to carry an EXACT factor,
                  // and a cell whose prevent-plant buy-up election cannot be pinned
                  // resolves to a range instead (corn 0.55–0.65, soybeans 0.60–0.70).
                  // The range is computed, it is correct, and it was shown only on the
                  // expanded cell rows — so the farm row a reader actually scans said
                  // nothing at all while the figure sat one click away.
                  // Summing the lows and the highs is the same arithmetic the point
                  // total does, run on both edges, so the band is a real bound on the
                  // farm and not a widest-cell approximation of one.
                  const bandCells = ppCells.filter((c) => c.modelled && c.modelled.available
                    && !c.modelled.exact && c.modelled.low != null && c.modelled.high != null);
                  const modBand = (modTotal == null && bandCells.length === ppCells.length && ppCells.length)
                    ? { lo: +bandCells.reduce((s, c) => s + c.modelled.low, 0).toFixed(2),
                        hi: +bandCells.reduce((s, c) => s + c.modelled.high, 0).toFixed(2) }
                    : null;
                  return (
                    <React.Fragment key={f.farmerId || f.farm}>
                      <tr onClick={() => setOpenFarm((m) => ({ ...m, [f.farmerId]: !shown }))}
                          style={{ cursor: "pointer", borderTop: "1px solid #eef3ee",
                                   background: f.actionable ? "#fdf6f4" : "transparent" }}>
                        <td style={{ padding: "7px 8px", fontWeight: 800, color: "#1a2e1a" }}>
                          {f.farm || "farm not on file"}
                          {f.allAgree && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: "#2d6a2d" }}>
                            ✓ all {f.cells.length} match</span>}
                          {/* ⚠ ONE VOCABULARY, NOT FIVE. The row carried up to four chips in
                              four different phrasings — "✓ all 3 match", "0 match · 1 not
                              compared", "2 to look at", "4 worksheet lines disagree with
                              itself" — so every row had to be parsed rather than scanned,
                              and "not compared" lumped a pending claim in with a harvest
                              loss nobody can estimate. The breakdown now uses the SAME
                              words as the partition strip above, in the same order, and
                              only names the lanes this farm actually has. */}
                          {!f.allAgree && (
                            <span style={{ marginLeft: 8, fontSize: 10.5, color: "#60735f", fontVariantNumeric: "tabular-nums" }}>
                              {f.cells.length} cells{" "}
                              {[["agree", "#2d6a2d"], ["look", "#8a3324"], ["pending", "#8a6d1f"],
                                ["unpaid", "#8a3324"], ["notest", "#5c6f5c"], ["other", "#8a3324"]]
                                .map(([k, fg]) => [k, fg, f.cells.filter(xLanes.is[k]).length])
                                .filter(([, , n]) => n > 0)
                                .map(([k, fg, n], i) => (
                                  <span key={k} style={{ color: fg, fontWeight: k === "agree" || k === "notest" ? 600 : 800 }}>
                                    {i > 0 ? " · " : "· "}{n} {CX_LANE_WORD[k]}
                                  </span>
                                ))}
                            </span>
                          )}
                          {/* Counted SEPARATELY from `actionable`, because it is a different
                              question about the same farm — their worksheet against itself,
                              not against our estimate. Folding the two counts into one would
                              be the provenance conflation this whole merge is avoiding. */}
                          {xcLines > 0 && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: "#8a3324" }}>
                            {xcLines} worksheet {xcLines === 1 ? "line" : "lines"} disagree
                            {xcLines === 1 ? "s" : ""} with itself</span>}
                        </td>
                        {/* OURS. A partial total says so rather than reading as the whole farm. */}
                        <td style={{ padding: "7px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                                     fontWeight: 700, color: modTotal == null && !modBand ? "#60735f" : "#1a2e1a" }}>
                          {modTotal != null ? money(modTotal)
                            : modBand ? <span style={{ fontSize: 11.5 }}>{money(modBand.lo)}–{money(modBand.hi)}</span>
                            : "—"}
                          {/* A band is shown as a band and labelled as one, so nobody
                              reads its low edge as a point estimate. */}
                          {modBand && <div style={{ fontSize: 10.5, fontWeight: 600, color: "#8a6d1f" }}>
                            PP buy-up not pinned</div>}
                          {modPartial && <div style={{ fontSize: 10.5, fontWeight: 600, color: "#8a6d1f" }}>
                            {modCells.length} of {ppCells.length} cells</div>}
                        </td>
                        {/* ⚠ A PARTIAL EXPECTATION MUST NOT SIT BESIDE A WHOLE-FARM STATEMENT.
                            Kevin, 2026-09-30: "Sullivan Makes no sense right now." His row read
                            WE EXPECT $21,682 (3 of 4 cells) · THEY STATED $34,482 · AIP PAID
                            $34,482 · DIFFERENCE —, and the eye subtracts the first two and finds
                            a $12,800 hole that does not exist: the missing cell is LIVINGSTON
                            DRY BEANS at $12,801, which we print as "can't compute" one line
                            below. Every figure was right and the ROW invited a wrong conclusion,
                            which is the failure this whole page exists to avoid.
                            So when our side covers only some cells, the carrier's side is shown
                            over THE SAME CELLS. Like compared with like; the whole-farm figure is
                            still there, in the muted line, named as covering more. */}
                        <td style={{ padding: "7px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {modPartial ? <>
                            {money(+modCells.reduce((s, c) => s + (c.expected || 0), 0).toFixed(2))}
                            <div style={{ fontSize: 10.5, color: "#60735f" }}>{money(f.expected)} over all {ppCells.length}</div>
                          </> : money(f.expected)}
                        </td>
                        <td style={{ padding: "7px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {modPartial ? <>
                            {money(+modCells.reduce((s, c) => s + (c.paidPp || 0), 0).toFixed(2))}
                            <div style={{ fontSize: 10.5, color: "#60735f" }}>{money(f.paid)} over all {ppCells.length}</div>
                          </> : money(f.paid)}
                        </td>
                        {/* ⚠ RED MEANS "DO SOMETHING", NOT "THE NUMBERS DIFFER". A farm whose
                            only gap is a claim still in adjustment showed a large red figure
                            with nothing to act on — Marquart Bros read −$308,131 and Woznica
                            −$71,323 while both are simply mid-season. The money is still shown,
                            because it is real and unpaid; what changes is that it reads as
                            pending rather than as a shortfall. */}
                        <td style={{ padding: "7px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                                     fontWeight: f.actionable ? 800 : 400,
                                     color: Math.abs(diff) < 1 ? "#6b7a6b" : (f.actionable ? "#8a3324" : "#8a6d1f") }}>
                          {Math.abs(diff) < 1 ? "—" : money(diff)}
                          {!f.actionable && Math.abs(diff) >= 1 && (
                            <div style={{ fontSize: 10.5, fontWeight: 600, color: "#8a6d1f" }}>in adjustment</div>
                          )}
                        </td>
                        <td style={{ padding: "7px 8px", color: "#60735f", fontSize: 11 }}>{shown ? "▾" : "▸"}</td>
                      </tr>
                      {shown && f.cells.map((c) => (
                        <tr key={c.key} style={{ borderTop: "1px solid #f6f9f6" }}>
                          <td style={{ padding: "5px 8px 5px 22px", color: "#33402f" }}>
                            {c.county} · <b>{c.crop}</b>
                            <span style={{ color: "#60735f", fontSize: 11 }}> · {c.aip ? c.aip.replace(/_/g, " ") : ""}</span>
                            {c.lane === CX_LANE.NOTHING_PAID && (
                              <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800,
                                             color: c.farmHasOpenClaim ? "#8a6d1f" : "#8a3324" }}>
                                {c.farmHasOpenClaim ? "nothing paid — claim still open" : "NOTHING PAID"}
                              </span>
                            )}
                            {c.lane === CX_LANE.NOT_ESTIMATED && (
                              <span style={{ marginLeft: 8, fontSize: 10.5, color: "#8a6d1f" }}>not estimated</span>
                            )}
                          </td>
                          {/* OUR figure for this cell — a number when the factor is exact,
                              a BAND when the buy-up election is not pinned, and a stated
                              reason when an input is missing. Never their number. */}
                          <td style={{ padding: "5px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                                       fontWeight: 700 }}>
                            {(() => {
                              const m = c.modelled;
                              if (!m) return <span style={{ color: "#60735f", fontWeight: 400 }}>—</span>;
                              if (!m.available) return (
                                <span style={{ color: "#8a6d1f", fontWeight: 600, fontSize: 10.5 }}
                                      title={"Cannot compute independently — " + (m.missing || []).join("; ")}>
                                  can't compute
                                </span>
                              );
                              if (!m.exact) return (
                                <span style={{ color: "#8a6d1f", fontSize: 11 }} title={m.why}>
                                  {money(m.low)}–{money(m.high)}
                                </span>
                              );
                              return <span title={m.why}>{money(m.expected)}</span>;
                            })()}
                          </td>
                          <td style={{ padding: "5px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(c.expected)}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(c.paidPp)}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                                       fontWeight: c.lane === CX_LANE.DIFFERS ? 800 : 400,
                                       color: c.lane === CX_LANE.DIFFERS ? "#8a3324" : "#6b7a6b" }}>
                            {c.difference == null ? "—" : (Math.abs(c.difference) < 1 ? "—" : money(c.difference))}
                            {/* ⚠ THE ADJUSTER NAMES THE REASON IT KNOWS. Kevin, 2026-09-30:
                                "I think Lang farms was paid $21 less than expected because he
                                ran out of eligibility; our internal adjuster shouldve caught
                                that." He was right. PP eligibility caps PLANTED + PREVENT
                                PLANT, not PP alone — LANG's corn is 36.7 planted + 36.0
                                reported PP = 72.7 against a 72.6 maximum, so 0.1 ac is not
                                payable and the carrier settled on 35.9 determined acres. That
                                is the whole $21, and it was printing as an unexplained gap.
                                Shown only where the overrun ACCOUNTS for the shortfall —
                                an unrelated difference must not be explained away by a
                                coincidence of size. */}
                            {(() => {
                              const e = c.eligibility;
                              if (!e || !e.exceeds || c.difference == null || c.difference >= 0) return null;
                              const perAc = c.expected && e.reportedAcres ? c.expected / e.reportedAcres : null;
                              const worth = perAc ? perAc * e.overAcres : null;
                              const explains = worth != null && Math.abs(Math.abs(c.difference) - worth) <= Math.max(2, worth * 0.05);
                              return (
                                <div style={{ fontSize: 10.5, fontWeight: 600, color: explains ? "#8a6d1f" : "#60735f", marginTop: 2 }}
                                     title={e.why}>
                                  {explains
                                    ? `${e.overAcres} ac over PP eligibility ≈ ${money(worth)}`
                                    : `${e.overAcres} ac over PP eligibility`}
                                </div>
                              );
                            })()}
                          </td>
                          <td style={{ padding: "5px 8px" }}>
                            {c.expectedDetail && c.expectedDetail.basis === CX_BASIS.PP_STATED && (
                              <button onClick={() => setOpenCell(c)} style={{ ...btnS, fontSize: 10.5 }}>how?</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {/* ── THE CARRIER'S WORKSHEET AGAINST ITSELF ──────────────────
                          Same three columns, different authority — so the left-hand
                          figure is labelled with where it came from, on every row. The
                          amounts are deliberately NOT summed into the farm row above:
                          they describe the same payment from the other side, and adding
                          them would count one claim twice. */}
                      {shown && xcHere.map((e) => e.rows.map((r, i) => (
                        <tr key={e.claim.key + "|" + i} style={{ borderTop: "1px solid #f6f9f6", background: "#fdf9f4" }}>
                          <td style={{ padding: "5px 8px 5px 22px", color: "#33402f" }}>
                            Claim {e.claim.claimNumber} · <b>{r.stage}</b>
                            {/* ⚠ THE CROP, BECAUSE A STAGE WITHOUT ONE IS READ AGAINST
                                WHATEVER CELL IS ABOVE IT. Sullivan holds two CY2026
                                claims — 00000047 is WHEAT and every P21 line on it is
                                stage `H`, while 00000066 is dry beans / grain sorghum /
                                soybeans at `P2`. Printed with no crop, under a cell list
                                that includes MONROE · WHEAT, the prevent-plant lines read
                                as the wheat claim's. Unresolved prints NOTHING rather than
                                borrowing the crop of the row above. */}
                            {r.crop ? <> · <b>{r.crop}</b>{r.county ? ` · ${r.county}` : ""}</> : null}
                            {r.acres ? ` · ${Math.round(r.acres)} ac` : ""}
                            <span style={{ color: "#60735f", fontSize: 11 }}>
                              {" · "}{e.claim.aip ? e.claim.aip.replace(/_/g, " ") : ""} · CY{e.claim.cropYear}
                            </span>
                            <div style={{ fontSize: 10, color: "#8a6d1f", fontWeight: 700, marginTop: 1 }}>
                              ↑ {r.from} — not our estimate
                            </div>
                          </td>
                          {/* No OUR-figure here: a cross-check row is the carrier against
                              itself, a different comparison in the same columns. Blank, never
                              filled with their number. */}
                          <td style={{ padding: "5px 8px", textAlign: "right", color: "#60735f" }}>—</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(r.theirFigure)}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(r.paid)}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                                       fontWeight: 800, color: "#8a3324" }}>
                            {r.diff == null ? "—" : money(r.diff)}
                          </td>
                          <td style={{ padding: "5px 8px" }} />
                        </tr>
                      )))}
                    </React.Fragment>
                  );
                };
                /* ⚠ THREE GROUPS, AND THE MIDDLE ONE IS THE WHOLE POINT. About $830,000 of
                   red sat in this table with nothing to act on — Marquart Bros −$308,131,
                   Thomas Adam Marquart −$228,231, Aaron H Marquart −$189,835, Woznica
                   −$71,323, Marquart Dairy −$32,417 — every dollar a claim still in
                   adjustment. The cell rows said so; the FARM row said "0 match · 5 not
                   compared" beside a six-figure negative, which reads as a shortfall.
                   A heading says it once, where a badge on every row could not, and the
                   two farms that need a person sit at the top rather than sorted among
                   seventy-seven that do not. */
                return [
                  ["look", "To look at", "our estimate disagrees with what was paid, or a worksheet disagrees with itself", "#8a3324", "#fdf0ec"],
                  ["pending", "Pending — claim still in adjustment", "unpaid, and the carrier has not finished. Not a shortfall", "#8a6d1f", "#fbf5e6"],
                  ["rest", "Reconciled, or nothing to compare against", "every cell agrees, or is a harvest loss this book cannot estimate", "#2d6a2d", "#f0f4ef"],
                ].map(([k, title, why, fg, bg]) => {
                  const list = xGroups[k] || [];
                  if (!list.length) return null;
                  /* ⚠ "6 FARMS" UNDER A STRIP THAT SAYS "2 TO LOOK AT" READS AS A
                     CONTRADICTION, and it is not one: two farms have a cell where our
                     estimate disagrees, and four have a worksheet that disagrees with
                     ITSELF — a different question about a different document, counted
                     separately everywhere else on this page for exactly that reason.
                     The heading splits them rather than leaving the reader to reconcile
                     two numbers that were never the same number. */
                  const nCells = k === "look" ? list.filter((f) => f.actionable > 0).length : 0;
                  const nSheet = k === "look" ? list.filter((f) => (xcByFarm.get(f.farmerId) || []).length > 0).length : 0;
                  /* The reconciled group is the one Kevin asked to collapse — "any claims
                     that match exactly can be collapsed" — and it is most of the book. It
                     opens on a click and says how many it is hiding, because a section
                     that merely vanishes reads as a farm list that lost rows. */
                  const open = k !== "rest" || xRestOpen;
                  return (
                    <React.Fragment key={"grp|" + k}>
                      <tr {...(k === "rest" ? { onClick: () => setXRestOpen((v) => !v), style: { cursor: "pointer" } } : null)}>
                        <td colSpan={6} style={{ padding: "9px 8px 5px", background: bg, borderTop: "2px solid #e4ece4" }}>
                          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: fg }}>
                            {k === "rest" ? (open ? "▾ " : "▸ ") : ""}{title}
                          </span>
                          <span style={{ fontSize: 11, color: "#6b7a6b", fontWeight: 700 }}> · {list.length} farm{list.length === 1 ? "" : "s"}</span>
                          {k === "look" && (nCells > 0 || nSheet > 0) && (
                            <span style={{ fontSize: 11, color: "#6b7a6b" }}>
                              {" ("}{nCells > 0 ? `${nCells} with a cell to look at` : ""}
                              {nCells > 0 && nSheet > 0 ? " · " : ""}
                              {nSheet > 0 ? `${nSheet} with a worksheet that disagrees with itself` : ""}{")"}
                            </span>
                          )}
                          <span style={{ fontSize: 11, color: "#7c8c7c" }}> — {why}</span>
                          {k === "rest" && !open && (
                            <span style={{ fontSize: 11, color: "#2d6a2d", fontWeight: 800 }}> · show them</span>
                          )}
                        </td>
                      </tr>
                      {open && list.map(farmRow)}
                    </React.Fragment>
                  );
                });
              })()}
                {/* A finding no farm could be named for is SHOWN, never dropped — an
                    unattributable claim is exactly the kind a reader needs to see. */}
                {xcUnplaced.map((e) => e.rows.map((r, i) => (
                  <tr key={"unplaced|" + e.claim.key + "|" + i} style={{ borderTop: "1px solid #eef3ee", background: "#fdf9f4" }}>
                    <td style={{ padding: "6px 8px", color: "#33402f" }}>
                      <b style={{ color: "#60735f", fontStyle: "italic" }}>farm not on file</b>
                      {" · "}Claim {e.claim.claimNumber} · <b>{r.stage}</b>
                      {/* Same rule as the farm-attached rows: a stage with no crop reads
                          against whatever sits above it. */}
                      {r.crop ? <> · <b>{r.crop}</b>{r.county ? ` · ${r.county}` : ""}</> : null}
                      <div style={{ fontSize: 10, color: "#8a6d1f", fontWeight: 700, marginTop: 1 }}>
                        ↑ {r.from} — not our estimate
                      </div>
                    </td>
                    {/* Blank for the same reason as the farm-attached rows above. */}
                    <td style={{ padding: "6px 8px", textAlign: "right", color: "#60735f" }}>—</td>
                    <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(r.theirFigure)}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(r.paid)}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 800, color: "#8a3324" }}>
                      {r.diff == null ? "—" : money(r.diff)}
                    </td>
                    <td style={{ padding: "6px 8px" }} />
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
          {!xFarms.length && <div style={{ fontSize: 12.5, color: "#6b7a6b", padding: "10px 2px" }}>
            No coverage carrying a loss in CY{expectedYear}.</div>}
        </div>
      )}

      {/* The derivation, and there is nothing to hand-wave: it is the carrier's own
          per-unit prevent-plant guarantee, listed. */}
      {openCell && openCell.expectedDetail && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
              How we got {money(openCell.expected)} — {openCell.farm} · {openCell.county} · {openCell.crop}
            </div>
            <button onClick={() => setOpenCell(null)} style={btnS}>close</button>
          </div>
          <div style={{ fontSize: 12.5, color: "#5c6f5c", margin: "6px 0 10px", lineHeight: 1.5 }}>
            This is <b>not an estimate of ours</b>. A prevent-plant loss pays the prevent-plant
            guarantee — there is no production to count against it — and the AIP states that
            guarantee per unit, in dollars. Measured over the {expectedYear} book, 113 of 115 paid
            prevent-plant cells match it to the dollar.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ color: "#6b7a6b", fontSize: 10.5, textAlign: "left" }}>
              <th style={{ padding: "4px 6px" }}>Unit</th><th style={{ padding: "4px 6px", textAlign: "right" }}>PP acres</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>APH</th><th style={{ padding: "4px 6px", textAlign: "right" }}>Level</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Price</th><th style={{ padding: "4px 6px", textAlign: "right" }}>Share</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Stated guarantee</th>
            </tr></thead>
            <tbody>
              {openCell.expectedDetail.inputs.map((u, i) => (
                <tr key={i} style={{ borderTop: "1px solid #f2f6f2" }}>
                  <td style={{ padding: "4px 6px" }}>{u.unit || "—"}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>{u.acres == null ? "—" : u.acres.toFixed(1)}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>{u.aph ?? "—"}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>{u.coverage_level == null ? "—" : Math.round(u.coverage_level * 100) + "%"}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>{u.price_election == null ? "—" : "$" + u.price_election}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>{u.share == null ? "—" : u.share}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>{money(u.liability)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid #dde8d8", fontWeight: 800 }}>
                <td style={{ padding: "5px 6px" }} colSpan={6}>Expected</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>{money(openCell.expected)}</td>
              </tr>
              <tr><td style={{ padding: "3px 6px" }} colSpan={6}>AIP paid</td>
                <td style={{ padding: "3px 6px", textAlign: "right" }}>{money(openCell.paidPp)}</td></tr>
            </tbody>
          </table>
          {openCell.stages && openCell.stages.length > 0 && (
            <div style={{ fontSize: 11.5, color: "#6b7a6b", marginTop: 8 }}>
              Paid across {openCell.stages.map((s) => `${s.stage} ${money(s.paid)} (${s.lines} line${s.lines === 1 ? "" : "s"})`).join(" · ")}
            </div>
          )}
        </div>
      )}


      {/* ⚠ THE LATCH. Kevin, 2026-09-23: "we need to estimate before it's paid, that's the
          whole point of us doing this." An estimate recomputed after the payment lands can
          always be made to agree, and nobody reading it later can tell whether it was — so a
          frozen row is the only kind worth storing, and it proves nothing otherwise.
          `payment_known_at_estimate` is stamped SERVER-SIDE from the carrier's own worksheet
          at write time, so a row written after the money arrived is permanently NOT a
          forecast and no client can talk it out of that. */}
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>
          Our estimate vs what they paid
        </div>
        {est && est.available === false ? (
          <div style={{ fontSize: 12.5, color: "#8a6d1f", fontWeight: 600 }}>
            ⚠ Estimates are not being recorded yet — <code>sql/074_claim_estimates.sql</code> has not been run.
          </div>
        ) : !est || !est.estimates || !est.estimates.length ? (
          <div style={{ fontSize: 12.5, color: "#5c6f5c", lineHeight: 1.5 }}>
            No estimates recorded yet. Record one on a claim <b>before</b> the carrier pays it and it
            is compared here automatically when the payment arrives. An estimate entered after payment
            is still stored, but it is marked as not a forecast — it cannot be evidence that we called it.
          </div>
        ) : (
          <div>
            {estRows.map((e) => (
              <div key={e.row.id} style={{ borderTop: "1px solid #f2f6f2", padding: "7px 0", fontSize: 12.5 }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
                  <b>{e.row.crop || "—"}{e.row.county ? " · " + e.row.county : ""}</b>
                  <span style={{ color: "#6b7a6b", fontSize: 12 }}>
                    CY{e.row.crop_year}{e.row.stage_code ? " · " + e.row.stage_code : ""}
                    {" · estimated "}{String(e.row.estimated_at || "").slice(0, 10)}
                  </span>
                  <span style={{ fontWeight: 800,
                    color: e.cmp.verdict === CC_ESTIMATE.DISCREPANCY ? "#8a3324"
                         : e.cmp.verdict === CC_ESTIMATE.AGREES ? "#2d6a2d" : "#8a6d1f" }}>
                    {String(e.cmp.verdict).replace(/_/g, " ")}
                  </span>
                  {/* ⚠ THE BADGE THAT MAKES THE ROW HONEST. Without it a row recorded after the
                      money arrived looks exactly like one that predicted it. */}
                  {!e.cmp.forecast && (
                    <span style={{ fontSize: 10.5, fontWeight: 800, color: "#6b4f12", background: "#fdf3dc",
                      border: "1px solid #e6c98a", borderRadius: 4, padding: "1px 6px" }}>NOT A FORECAST</span>
                  )}
                </div>
                <div style={{ color: "#5c6f5c", marginLeft: 2 }}>{describeEstimate(e.cmp)}</div>
              </div>
            ))}
            <div style={{ fontSize: 11, color: "#60735f", marginTop: 8, lineHeight: 1.5 }}>
              Flagged when the gap is more than the greater of {money(CC_EST_TOL.dollars)} or{" "}
              {Math.round(CC_EST_TOL.pct * 100)}% of our estimate. The dollars and the percentage are on
              each line so you can disagree with the label.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ClaimsTrackingView({ farmers, isMobile, onSelectFarmer, mode = "admin", claimManager = false, restrictFarmerIds = null }) {
  // `claimManager` opens the Claim Manager board to a scoped sub-agent (portal role="agent")
  // even though it's otherwise an admin-only tool hidden in portal. restrictFarmerIds scopes
  // it to their customers.
  const showManager = mode !== "portal" || claimManager;
  // A GROWER READING THEIR OWN CLAIMS — not an agent working a book. They get
  // cards rather than an agency table, and lose the controls that only make
  // sense across many farms (farmer search, AIP filter, farmer count).
  const customerView = mode === "portal" && !claimManager;
  // Claim Manager (whole-book roster/outreach tool) + the prior-year refresh sync
  // are ADMIN tools — hidden in portal. Fail-closed on mode so a preview-as admin
  // (whose isCurrentUserAdmin() is still true) doesn't leak them either.
  const isPortal = mode === "portal";
  const [filterAIP, setFilterAIP] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterStage, setFilterStage] = useState("all");  // claim stage / type (Prevent Plant, Harvest Loss, …)
  const [filterAdjuster, setFilterAdjuster] = useState("all");
  const [filterYear, setFilterYear] = useState(String(CURRENT_CROP_YEAR));   // default to current crop year (guarded by effYear below)
  const [filterCrop, setFilterCrop] = useState("all");
  const [filterFarmer, setFilterFarmer] = useState("");
  const [sortBy, setSortBy] = useState("date");
  // Land on the Claim Manager (the working board) for admins/agents; portal farmers (no
  // manager) land on their Active claims. "active" (in-progress) | "paid" (cleared) | "check".
  const [tab, setTab] = useState(showManager ? "check" : "active");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");

  // Per-claim loss acres from M13 P21 stage lines (prevent-plant / replant /
  // harvest acres). Keyed by policy|cropYear|claimNumber to match each claim row.
  // Open claims have no P21 lines yet → no acres until the AIP processes them.
  const [lossAcresByClaim, setLossAcresByClaim] = useState({});
  // Claims (keyed policy|cropYear|claimNumber) that carry a REAL ECO/SCO P21
  // line (stage_code 'FL'). This is the ONLY trustworthy ECO signal. loss_code
  // D is NO LONGER exclusive to ECO — NAU issues D on ordinary prevent-plant /
  // replant disbursements too, so a bare D must NOT be labeled ECO/SCO. A D row
  // is ECO only when its claim actually has an FL line here. (Verified 2026-07-14:
  // the July CY2026 "ECO/SCO" rows — Brian Thompson corn, Bay Farms soybeans —
  // were D-coded PP/Replant claims with no FL line, mislabeled ECO in the portal.
  // ECO/SCO is an area plan that can't even pay a 2026 crop until ~mid-2027.)
  const [ecoClaimKeys, setEcoClaimKeys] = useState(() => new Set());
  useEffect(() => {
    (async () => {
      try {
        const lines = await sbFetch("indemnity_lines", { select: "policy_id,crop_year,loss_key_base,acres,source,stage_code" }).catch(() => []);
        const m = {};
        const eco = new Set();
        (lines || []).forEach(ln => {
          if (ln.source !== 'p21') return;
          const k = `${ln.policy_id}|${ln.crop_year || ''}|${ln.loss_key_base || ''}`;
          m[k] = (m[k] || 0) + (Number(ln.acres) || 0);
          if (ln.stage_code === 'FL') eco.add(k);   // real ECO/SCO area-plan loss line
        });
        setLossAcresByClaim(m);
        setEcoClaimKeys(eco);
      } catch { /* non-fatal: acres column just stays blank */ }
    })();
  }, []);

  // ── Refresh prior-year (CY2025) indemnities ───────────────────────────────
  // Daily sync only reads the CURRENT-year M13 / NAU file. Wheat ECO + other
  // prior-year settlements land in the CY2025 file as they pay throughout the
  // year — including the 5/14 + 5/15 batches that triggered this work. This
  // button calls /api/nau-sync?priorYear=1 + /api/m13-sync?priorYear=1 using
  // the admin Supabase session. A daily cron at 13:00 UTC keeps this auto,
  // but the button gives an in-place "I just paid 30 farmers, refresh now"
  // option without hunting for the Sync Log screen.
  async function refreshPriorYear() {
    setRefreshing(true);
    setRefreshMsg("Pulling NAU prior-year file...");
    try {
      const token = localStorage.getItem("cloverag_access_token");
      if (!token) throw new Error("Not signed in — log out and back in, then try again.");
      const headers = { "Authorization": `Bearer ${token}` };
      const nauRes = await fetch("/api/nau-sync?priorYear=1", { method: "POST", headers });
      const nauJson = await nauRes.json().catch(() => ({}));
      if (!nauRes.ok) throw new Error(`NAU prior-year refresh failed: ${nauJson.error || nauRes.status}`);
      const nauCount = nauJson.indemnities || nauJson.imported?.indemnities || 0;
      setRefreshMsg(`NAU ✅ ${nauCount} indemnity rows. Pulling R&H prior-year file...`);
      const rhRes = await fetch("/api/m13-sync?priorYear=1", { method: "POST", headers });
      const rhJson = await rhRes.json().catch(() => ({}));
      if (!rhRes.ok) throw new Error(`R&H prior-year refresh failed: ${rhJson.error || rhRes.status}`);
      const rhCount = rhJson.indemnities || rhJson.imported?.indemnities || 0;
      setRefreshMsg(`✅ NAU ${nauCount} + R&H ${rhCount} prior-year indemnity rows refreshed. Reloading...`);
      // Auto-reload so the new rows appear without the user having to remember
      // to hit Ctrl+R. The DB has fresh data, the in-memory React state is
      // stale — only a full reload re-runs loadData() and rebuilds farmer.claims.
      // Use replace() to avoid back-button cluttering with the same URL.
      setTimeout(() => { window.location.reload(); }, 800);
    } catch (e) {
      setRefreshMsg(`❌ ${e.message}`);
    }
    setRefreshing(false);
  }

  // One row per real M13 disbursement (check), so every row carries its TRUE
  // check date. ECO is still cleanly separated (loss_code D → "ECO/SCO" via
  // renderStageCell). PP vs Harvest can't be split into their own dated rows
  // because M13 doesn't tag a check with a stage — the per-stage *amounts* live
  // on the farmer card (P21), but inventing a per-stage payment date here gave
  // wrong dates, so we don't.
  // ⚠ THE REAL CLAIMS ONLY. A filed Notice of Loss becomes a claim row further
  // down, and `noticeBoard` READS this list to decide which notices a claim
  // already covers — so folding the notices in here would be circular and they
  // would suppress themselves on the second pass. baseClaims -> noticeBoard ->
  // allClaims is the order that has no cycle in it.
  const showLrpClaims = canSeeLrpIndemnity();
  const baseClaims = useMemo(() => {
    const items = [];
    farmers.forEach(f => {
      (f.claims || []).forEach(c => {
        // LRP claims are admin-only (canSeeLrpIndemnity) — absent, not zeroed.
        if (c.policyType === "lrp" && !showLrpClaims) return;
        const lossAcres = lossAcresByClaim[`${c.policyId}|${c.cropYear || ''}|${c.claimNumber || ''}`] || null;
        items.push({ ...c, farmerName: f.name, farmerId: f.id, farmerObj: f, lossAcres });
      });
    });
    return items;
  }, [farmers, lossAcresByClaim, showLrpClaims]);

  // Active vs Paid split. "Paid" = a check has cleared. Everything else is an
  // in-progress claim: Open (NAU adjuster assigned, no loss header / $ yet),
  // Pending (R&H loss header, no disbursement), Verified (AIP confirmed the
  // indemnity, check not cleared), Adjustment (quality ride-along). The Active
  // tab is the early-warning surface — Sullivan's open soybean REP claim and
  // Marquart's open wheat HAR show up here the morning after they're filed.
  const isPaidStatus = (s) => s === "Paid";
  // "Closed" = a prior-year notice that resolved with no indemnity (see the
  // claim builder). It's neither active work nor a paid indemnity, so it shows
  // on NEITHER book-wide tab — it stays auditable on the farmer card.
  //
  // ── RELEASED IS THE CARRIER'S OWN WORD FOR THE SAME THING (Kevin, 2026-09-24) ──
  // *"Released claims should no longer be in active claims."*
  //
  // The test was "not Paid and not Closed", so `Released` — which is neither — read as
  // ACTIVE and sat on the early-warning tab forever. Measured across the whole
  // indemnities table: **66 Released rows, every one of them R&H**, and
  //
  //   indemnity_amount > 0 ......... 0 of 66
  //   claim_total > 0 .............. 0 of 66
  //   a check_date ................. 0 of 66
  //   the same claim also Paid ..... 0 of 66
  //
  // So a released claim is one the carrier closed having paid nothing — exactly what
  // `Closed` already describes, under R&H's own status word. CLAUDE.md records the same
  // shape from the other side: 0 of 47 CY2025 and 0 of 14 CY2026 Released claims carry
  // an intention, a notice date or a cause, while every other status has them.
  //
  // ⚠ AND IT COSTS NOTHING IF A RELEASED CLAIM EVER REOPENS. The status is SYNC-OWNED and
  // rewritten daily from the AIP's own file, so a claim that came back would simply stop
  // being Released and reappear. Terminal here means "terminal until the carrier says
  // otherwise", not a decision we have to get right forever.
  //
  // It stays visible on the farmer card, like Closed — set aside, not deleted.
  // The rule itself (CLAIM_TERMINAL_NO_PAYMENT / isActiveStatus) moved to module scope,
  // beside AdjusterPanel, so the provisional prevent-plant gate asks this tab's own
  // question rather than a copy of it. This tab uses that one definition.

  // Parse a comma-sep claim_stages string ("HAR,PP,REP") into AIP-aware label
  // parts, collapsing the UH→H accounting split (CLAIM_STAGE_GROUP). Each part
  // carries a `confirmed` flag so undecoded codes (SCT/P2/R&H-FL) can render
  // muted instead of masquerading as a known stage. Never guesses a meaning.
  const formatStageParts = (stagesStr, aip) => {
    if (!stagesStr) return null;
    const seen = new Set(); let parts = [];
    for (const raw of String(stagesStr).split(",").map(s => s.trim()).filter(Boolean)) {
      const code = CLAIM_STAGE_GROUP[raw] || raw;
      if (seen.has(code)) continue;
      seen.add(code);
      parts.push({ code, label: stageLabelFor(code, aip), confirmed: isStageConfirmed(code, aip) });
    }
    // Clean up the cell so a real stage never shares space with a "?" placeholder:
    //  • When any CONFIRMED stage is present, drop every unconfirmed code. An
    //    undecoded code (e.g. P2 — which in this book ALWAYS rides alongside PF/
    //    Prevent Plant, never alone) adds only a meaningless "?" next to a known
    //    stage. Hiding it asserts nothing about what P2 means — we just don't show
    //    a placeholder when we already have a real stage. This also subsumes the
    //    SCT case (SCT is unconfirmed): a notice-of-loss record drops off once the
    //    claim is adjusted to a real stage.
    //  • When EVERY code is unconfirmed, keep them (shown raw + "?") so the cell
    //    isn't blank — except still drop SCT if it's not the sole record, so a
    //    freshly-filed claim with only SCT still reads "Notice Filed" (e.g. TRI ACRES).
    if (parts.some(p => p.confirmed)) {
      parts = parts.filter(p => p.confirmed);
    } else if (parts.length > 1) {
      parts = parts.filter(p => p.code !== "SCT");
    }
    return parts.length ? parts : null;
  };

  // A claim is a real ECO/SCO only when it carries a P21 FL line (ecoClaimKeys)
  // — NOT merely because a disbursement is loss_code D. See ecoClaimKeys above.
  const claimHasEco = (c) =>
    ecoClaimKeys.has(`${c.policyId}|${c.cropYear || ''}|${c.claimNumber || ''}`);

  // Render the Stage column for a claim row. Stage codes are an MPCI (crop)
  // concept — PRF/DRP/LRP have none, so show "—". Confirmed codes render in the
  // stage color; unconfirmed (undecoded M13 codes) render muted + italic with a
  // tooltip so an agent never reads a raw code as a verified stage.
  const renderStageCell = (c) => {
    if (c.policyType && c.policyType !== "crop") return <span style={{ color:"#ccc" }}>—</span>;
    // loss_code D is the ECO/SCO area-plan payment ONLY when the claim actually
    // carries a P21 FL line (claimHasEco). D is no longer exclusive to ECO — NAU
    // now issues D on ordinary prevent-plant / replant disbursements — so a bare
    // D without an FL line is a normal loss, not ECO. Verified originally per
    // farmer that D total == P21 FL total on real ECO (NAU 2026-06-14; R&H
    // 2026-06-16). When it's real ECO, label ECO/SCO even though it inherits the
    // loss's P20X stages; otherwise fall through and show the real stage.
    if (c.lossCode === "D" && claimHasEco(c)) {
      return <span style={{ color:"#7a3b10", fontWeight:700 }}>ECO/SCO</span>;
    }
    const rawParts = formatStageParts(c.claimStages, c.aip);
    if (!rawParts) return <span style={{ color:"#ccc" }}>—</span>;
    // Two different stage codes can carry the same label (two prevent-plant
    // stages on one claim), which printed "Prevent Plant, Prevent Plant".
    // Display only: the Stage filter reads claimStageLabels, not this.
    const seenLabel = new Set();
    const parts = rawParts.filter(p => { const k = p.label + "|" + !!p.confirmed; if (seenLabel.has(k)) return false; seenLabel.add(k); return true; });
    return parts.map((p, i) => (
      <span key={i}>
        {i > 0 && <span style={{ color:"#bbb" }}>, </span>}
        <span
          title={p.confirmed ? undefined : `Unverified M13 stage code (${aipLabel(c.aip)}) — meaning not yet confirmed, shown as the raw code`}
          style={{ color: p.confirmed ? "#7a3b10" : "#9aa0a6", fontStyle: p.confirmed ? "normal" : "italic", fontWeight: p.confirmed ? 600 : 500 }}>
          {p.label}{p.confirmed ? "" : " ?"}
        </span>
      </span>
    ));
  };

  // Distinct human stage labels for a claim, collapsing the UH→H split the same
  // way formatStages does — used to drive (and match against) the Stage filter.
  // A multi-stage claim returns every label it carries so it matches any of them.
  const claimStageLabels = (c) => {
    // Stages (incl. ECO/SCO) are an MPCI/crop concept — PRF/DRP/LRP have none.
    // Mirror renderStageCell's guard so the Stage filter never leaks a non-crop
    // claim (e.g. a DRP claim whose disbursement happens to carry loss_code D).
    if (c.policyType && c.policyType !== "crop") return [];
    if (c.lossCode === "D" && claimHasEco(c)) return ["ECO/SCO"];   // D = ECO only when backed by a P21 FL line (see renderStageCell)
    // The stage mapping itself is shared with the All Customers claim filter.
    return claimStageTypeSet(c);
  };

  // ONE M13 CLAIM ROUTINELY NAMES SEVERAL CROPS, so the crop cell is a
  // comma-sep LIST, not a crop. Marquart Bros' CY2026 prevent-plant claim
  // 3600118 is "CORN, POTATOES" — five coverage ids on one claim number.
  // Matching the Crop filter against that joined string was wrong twice over:
  // a potato claim was invisible under "POTATOES" (the reported symptom), and
  // the dropdown filled with combos — "CABBAGE, CORN", "CORN, DRY BEANS,
  // SOYBEANS", "CORN, PROCESSING BEANS, SOYBEANS" — which are not crops and
  // which no agent would ever think to pick. Worse, OATS, CABBAGE and
  // TRITICALE reached the dropdown ONLY inside a combo, so there was no way
  // to filter to them at all. Split it and match on ANY crop the claim names.
  // This is the rule the farmer card's spring/fall split already follows (see
  // FarmerClaimsHistory): read every crop, allocate no dollars — M13 gives no
  // per-crop split of a claim's money.
  const claimCropList = (c) =>
    String(c.crop || "").split(",").map(s => s.trim().toUpperCase()).filter(Boolean);

  // ── Claim grouping ────────────────────────────────────────────────────────
  // M13 pays a claim as several disbursements — the real check plus M/F premium
  // credits (indemnity applied against premium owed), and separately the ECO/SCO
  // payment (loss_code D). Shown raw, one claim reads as 3 duplicate "corn
  // payments" with the acres repeated on each. Fold a claim's disbursements into
  // ONE row via this key, which deliberately NEVER merges across:
  //   • policy / crop / county           (different claims)
  //   • stage signature (PP vs REP vs H) (claimStageLabels — never fuse types)
  //   • base vs ECO (loss_code D)         (Harvest + ECO stay separate lines)
  // Only disbursements sharing a claim number (or loss_key) collapse together.
  const groupKeyOf = (c) => {
    // LOSS_KEY IS THE CLAIM, not the claim number. NAU reuses one claim_number
    // for two separate claims on the same policy and year — Sugar Branch's
    // CY2025 00000059 is loss_key 844973582-1 ($195,246) and -2 ($82,216), each
    // with its OWN P20 total. Keying on the number merges them into a single
    // $277,462 row that matches no statement the grower will ever hold. No
    // loss_key spans two claim numbers, so this is strictly the finer key; the
    // 62 rows with no loss_key (open, $0) still fall back to the number.
    const id = c.lossKey || c.claimNumber ||
      `${c.checkDate || c.claimDate || ''}|${c.indemnityAmount || 0}|${c.status || ''}`;
    return [
      c.policyId || c.farmerId || '',
      c.cropYear || '',
      (c.crop || '').toUpperCase(),
      (c.county || '').toUpperCase(),
      c.lossCode === 'D' ? 'ECO' : 'BASE',      // keep ECO/SCO off the base row
      claimStageLabels(c).join('+'),             // never merge different stages
      id,
    ].join('|');
  };
  const countClaims = (rows) => new Set(rows.map(groupKeyOf)).size;

  // ── Tie-out against M13 ───────────────────────────────────────────────────
  // Every disbursement we display must sum to the total the AIP states for that
  // claim (P20 f[8]). The check runs at the CLAIM level, not the displayed row:
  // one claim legitimately splits across several rows here (crop, county, stage,
  // and ECO kept off the base row), so a per-row comparison would cry wolf.
  // Keyed policy + crop year + claim number because AIP claim numbers are only
  // unique WITHIN a crop year — NAU issued 00000064 in both 2025 and 2026, and
  // dropping the year merges two different claims into one bogus mismatch.
  // R&H P20 carries no total, so those claims are counted as unverifiable here
  // rather than silently passing.
  const m13TieOut = useMemo(() => {
    const byClaim = new Map();
    for (const c of baseClaims) {
      if (!c.claimNumber) continue;
      // loss_key first, for the same reason groupKeyOf uses it: one claim number
      // can hold two claims with two separate AIP totals, and comparing their
      // combined sum against the larger total reports a $92,069 phantom gap.
      const k = c.lossKey || `${c.policyId || ''}|${c.cropYear || ''}|${c.claimNumber}`;
      let g = byClaim.get(k);
      if (!g) { g = { stated: null, sum: 0 }; byClaim.set(k, g); }
      g.sum += (c.indemnityAmount || 0);
      if (c.claimTotal != null && (g.stated == null || c.claimTotal > g.stated)) g.stated = c.claimTotal;
    }
    let checked = 0, off = 0, variance = 0, unverifiable = 0;
    for (const g of byClaim.values()) {
      if (g.stated == null) { unverifiable++; continue; }
      checked++;
      const d = g.stated - g.sum;
      if (Math.abs(d) >= 0.01) { off++; variance += d; }
    }
    return { checked, off, variance, unverifiable };
  }, [baseClaims]);

  // Claim years present in the data (newest first) — drives the Year dropdown.
  const years = [...new Set(baseClaims.map(c => c.cropYear).filter(Boolean))].sort((a, b) => b - a);
  // Guard the year filter. The default is the current crop year, but a portal
  // user whose only claims are prior-year has no option for it — the <select>
  // then shows "All Years" while the value stays e.g. "2026", silently hiding
  // every claim (Veazey-Post's 2025 PRF claims were invisible this way). Fall
  // back to "all" whenever the selected year isn't actually available.
  const effYear = (filterYear === "all" || years.some(y => String(y) === filterYear)) ? filterYear : "all";

  // ── NOTICES FILED, NOT YET IN ADJUSTMENT ──────────────────────────────────
  //
  // Kevin, 2026-09-08: "Many people who have claims open are showing as not
  // having a claim open. Brian Thompson is just one example ... He has a corn
  // silage appraisal claim opened right now."
  //
  // His policy's only CY2026 claim is a PREVENT-PLANT claim settled in July;
  // the silage appraisal notice was filed 2026-08-26 and has no indemnity row at
  // all. So the page said "Active 0 · Paid 1", which is true about the table and
  // false about his claims. Measured book-wide: 20 such cells across 17 farms.
  //
  // ⚠ THE LANE IS ADMIN-ONLY BECAUSE ITS SOURCE IS. m13_notice_of_loss carries a
  // single admin SELECT policy, so a scoped agent or a portal user reads ZERO
  // rows from it. RLS holds — this is not a leak — but a lane that prints a
  // confident 0 against a real 20 is worse than one that is not there. Same call
  // the corn chop lane already makes, for the same table.
  const noticesLaneVisible = !isPortal && isCurrentUserAdmin();
  const [noticeRows, setNoticeRows] = useState(null);   // null = not loaded, NEVER an empty list
  const [noticePols, setNoticePols] = useState(null);
  const [noticeErr, setNoticeErr] = useState("");
  // The year the lane answers for. "All years" has no single answer here — a
  // notice is outstanding relative to one crop year's claims — so it falls back
  // to the current crop year and the panel says which year it is showing.
  const noticeYear = /^\d{4}$/.test(effYear) ? Number(effYear) : CURRENT_CROP_YEAR;
  useEffect(() => {
    if (!noticesLaneVisible || tab !== "active") return;
    let dead = false;
    (async () => {
      try {
        setNoticeErr("");
        const [nl, pl] = await Promise.all([
          sbFetch("m13_notice_of_loss", { select: NOTICE_SELECT_COLUMNS, filters: [`crop_year=eq.${noticeYear}`] }),
          sbFetch("policies", { select: "id,farmer_id,policy_number,aip,crop_year", filters: [`crop_year=eq.${noticeYear}`] }),
        ]);
        if (dead) return;
        setNoticeRows(nl || []);
        setNoticePols(pl || []);
      } catch (e) {
        if (dead) return;
        // A failed read must not render as "no notices outstanding".
        setNoticeErr(e.message || "could not read the notice mirror");
        setNoticeRows(null);
      }
    })();
    return () => { dead = true; };
  }, [noticesLaneVisible, tab, noticeYear]);

  const noticeBoard = useMemo(() => {
    if (!noticeRows || !noticePols) return null;
    const nameById = new Map();
    (farmers || []).forEach(f => (f.allFarmerDbIds?.length ? f.allFarmerDbIds : [f.id])
      .forEach(id => { if (id) nameById.set(id, f.name); }));
    return outstandingNotices({
      notices: noticeRows,
      policies: (noticePols || []).map(p => ({
        id: p.id, farmerId: p.farmer_id, farmerName: nameById.get(p.farmer_id) || "",
        policyNumber: p.policy_number, aip: p.aip, cropYear: p.crop_year,
      })),
      claims: baseClaims,
      cropYear: noticeYear,
      isSyncFormat: isSyncFormatPolicyNumber,
      // The canonical map, never a copy — four of the twelve codes this mirror
      // sends were mis-named when they were typed by hand.
      cropForCode: (code) => RMA_CROPS[code] || null,
      // The Active tab's OWN test, so this lane suppresses exactly what that tab
      // shows. A positive whitelist here would drift from a negative rule.
      isActiveStatus,
    });
  }, [noticeRows, noticePols, baseClaims, noticeYear, farmers]);

  // ⚠ A FILED NOTICE OF LOSS *IS* AN OPEN CLAIM. Kevin, 2026-09-09: "I dont
  // understand what this notice of loss filed section is, shouldnt these just be
  // active claims?" — and he is right. The grower filed; the claim is open. The
  // only reason these ever sat in a section of their own is a DATA artifact: NAU
  // has not written a stage record, so there is no `indemnities` row for the
  // Active table to draw. That is our plumbing showing through onto his screen.
  //
  // They are ordinary rows now, carrying the status "Notice Filed".
  //
  // ⚠ AND THEY CANNOT REACH A DOLLAR, BY CONSTRUCTION RATHER THAN BY CARE:
  //   · `claimNumber: null` — m13TieOut skips any row without one, so a synthetic
  //     row can never enter the reconciliation against the AIP's stated total.
  //     A P58 notice genuinely HAS no claim number, so this is a fact, not a trick.
  //   · `indemnityAmount: null` — and totalIndemnity only sums Verified/Paid, so
  //     it contributes nothing to any money figure on the page.
  // This is the BUG-036 rule kept: a notice is shown as the open claim it is,
  // never as a fabricated $0 settlement.
  /* ⚠ A NOTICE ROW NAMES THE ADJUSTER HANDLING THAT POLICY'S GROUND.
   *
   * Kevin, 2026-10-06: "We need to see adjuster name on an active open claim."
   * He is right and the refusal before this was wrong. P58 carries no adjuster
   * field, so the notice row has none of its own — but the AIP assigns one when the
   * notice is filed, and this book already holds the name: the SAME policy's claim
   * for the same crop year states it. Measured on the three farms he was looking at,
   * CY2026 corn — MULLIGAN to WHEELER, LENA; CARLEY and PALLADINO to BURRELL,
   * DEREK. The names cluster by policy and carrier territory rather than by loss,
   * which is what makes this a lookup rather than a guess.
   *
   * ⚠ IT IS A STATED FACT ABOUT THE POLICY, AND THE HOVER SAYS WHICH. "the adjuster
   * on this policy's CY2026 CORN claim" is in our data; "the adjuster who will work
   * THIS notice" is not, and nobody is told it is.
   *
   * ⚠ SAME CROP FIRST, THEN THE POLICY. A policy can carry two crops with two
   * adjusters, so a corn notice takes corn's. Only where the crop cannot be matched
   * does it fall back to the policy, and the hover changes to say so.
   */
  const noticeAdjuster = useMemo(() => {
    const byCrop = new Map(), byPolicy = new Map();
    for (const c of baseClaims || []) {
      const nm = cleanAdjuster(c && c.adjuster);
      if (!nm || !c.policyId) continue;
      const yr = Number(c.cropYear);
      if (!Number.isFinite(yr)) continue;
      // `crop` is comma-joined on a multi-crop claim ("CORN, SOYBEANS"), so split
      // it — the same rule the billing credits follow. A joined label is never a key.
      for (const part of String(c.crop || "").split(",")) {
        const cr = part.trim().toUpperCase();
        if (!cr) continue;
        const k = c.policyId + "|" + yr + "|" + cr;
        if (!byCrop.has(k)) byCrop.set(k, nm);
      }
      const pk = c.policyId + "|" + yr;
      if (!byPolicy.has(pk)) byPolicy.set(pk, nm);
    }
    return { byCrop, byPolicy };
  }, [baseClaims]);

  const noticeClaims = useMemo(() => {
    if (!noticeBoard || !noticeBoard.rows.length) return [];
    const byId = new Map();
    (farmers || []).forEach(f => (f.allFarmerDbIds?.length ? f.allFarmerDbIds : [f.id])
      .forEach(id => { if (id) byId.set(id, f); }));
    return noticeBoard.rows.map(r => ({
      farmerName: r.farmerName, farmerId: r.farmerId, farmerObj: byId.get(r.farmerId) || null,
      policyId: r.policyId, policyNumber: r.policyNumber, aip: r.aip,
      cropYear: noticeYear,
      // A commodity the map cannot name renders RAW, the same convention every
      // other unsourced code here follows.
      crop: r.crop || String(r.commodityCode || ""),
      county: null,
      status: NOTICE_CLAIM_STATUS,
      claimNumber: null,
      // Stable identity for the cell, prefixed so it can never be mistaken for an
      // AIP loss key. groupKeyOf falls back to date|amount|status without it, which
      // two notices filed the same day on one policy would collide on.
      lossKey: "NOTICE:" + (r.policyId || r.policyNumber) + "|" + r.commodityCode,
      indemnityAmount: null,
      claimDate: r.noticeDate ? new Date(r.noticeDate).toISOString().slice(0, 10) : null,
      noticeDate: r.noticeDate ? new Date(r.noticeDate).toISOString().slice(0, 10) : null,
      checkDate: null, lossAcres: null, policyType: "crop",
      // Resolved above from this policy's own claims: same crop year, CROP FIRST so a
      // two-crop policy with two adjusters takes the right one, then the policy. Null
      // where neither matches, which leaves the cell on "adjuster not in file yet".
      ...(() => {
        const cr = String(r.crop || "").trim().toUpperCase();
        const hitCrop = cr ? noticeAdjuster.byCrop.get(`" + r.policyId + "|" + noticeYear + "|" + cr + "`) : null;
        const hitPol = noticeAdjuster.byPolicy.get(`${r.policyId}|${noticeYear}`) || null;
        const nm = hitCrop || hitPol;
        return {
          adjuster: nm || null,
          adjusterFrom: !nm ? null
            : hitCrop
              ? `From this policy's CY${noticeYear} ${cr} claim — the notice of loss (P58) carries no adjuster field of its own.`
              : `From this policy's CY${noticeYear} claims — the notice of loss (P58) carries no adjuster field, and no claim on this crop to match.`,
        };
      })(),
      noticeCount: r.noticeCount, noticeIntentions: r.intentions, priorPaid: r.priorPaid,
    }));
  }, [noticeBoard, noticeYear, farmers, noticeAdjuster]);

  const allClaims = useMemo(() => [...baseClaims, ...noticeClaims], [baseClaims, noticeClaims]);


  const filtered = useMemo(() => {
    let result = allClaims.filter(c => tab === "paid" ? isPaidStatus(c.status) : isActiveStatus(c.status));
    if (filterAIP !== "all") result = result.filter(c => c.aip === filterAIP);
    if (filterType !== "all") result = result.filter(c => (c.policyType || "crop") === filterType);
    if (filterStatus !== "all") result = result.filter(c => c.status === filterStatus);
    if (filterStage !== "all") result = result.filter(c => claimStageLabels(c).includes(filterStage));
    if (filterAdjuster !== "all") result = result.filter(c => cleanAdjuster(c.adjuster) === filterAdjuster);
    if (effYear !== "all") result = result.filter(c => String(c.cropYear || "") === effYear);
    if (filterCrop !== "all") result = result.filter(c => claimCropList(c).includes(filterCrop));
    if (filterFarmer.trim()) {
      // Case-insensitive substring match against farmerName so "mro" finds
      // "MROCZEK FARMS" and "sunrise" finds "SUNRISE AG LLC".
      const q = filterFarmer.trim().toUpperCase();
      result = result.filter(c => (c.farmerName || "").toUpperCase().includes(q));
    }

    // Sort by a GENUINE claim date (check_date > claim_date). We deliberately
    // do NOT fall back to created_at: M13 does a daily delete+reinsert, so
    // created_at is always "last night's sync" — using it pinned old dateless
    // R&H LRP "Verified" settlements (e.g. the Jan 23 premium credits) to the
    // top forever. Rows with no real date sort to the bottom (by amount) instead.
    if (sortBy === "date") result.sort((a, b) => {
      const da = a.checkDate || a.claimDate;
      const db = b.checkDate || b.claimDate;
      if (!da && !db) return (b.indemnityAmount || 0) - (a.indemnityAmount || 0);
      if (!da) return 1;
      if (!db) return -1;
      return new Date(db) - new Date(da);
    });
    else if (sortBy === "amount") result.sort((a, b) => (b.indemnityAmount || 0) - (a.indemnityAmount || 0));
    else if (sortBy === "farmer") result.sort((a, b) => (a.farmerName || "").localeCompare(b.farmerName || ""));
    return result;
  }, [allClaims, tab, filterAIP, filterType, filterStatus, filterStage, filterAdjuster, effYear, filterCrop, filterFarmer, sortBy]);

  // A farmer opening Claims and finding nothing is a data gap on our side as
  // often as it's a claim-free year — either way we want it counted separately
  // from a healthy visit. No-ops in the agent app.
  // `null` error, explicitly: allClaims is derived from the already-loaded
  // `farmers` prop, so this view has no fetch of its own that can fail out from
  // under it. The argument is required so that a screen WITH a load can never
  // drop it by accident — see usePortalEmptyState.
  usePortalEmptyState("claims", allClaims.length === 0, true, null);

  // One row per claim. Fold each claim's disbursements together (see groupKeyOf),
  // keeping a running total for the amount cell. rep = the largest disbursement
  // so the row's crop/county/stage/date reflect the real check, not a $60
  // premium-credit line. The how-it-was-paid caption comes from claimPayMix over
  // g.rows. Map preserves the sorted order of first-seen rows.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const c of filtered) {
      const k = groupKeyOf(c);
      let g = m.get(k);
      if (!g) { g = { rep: c, rows: [], total: 0, confirmed: 0 }; m.set(k, g); }
      g.rows.push(c);
      g.total += (c.indemnityAmount || 0);
      // Only a CONFIRMED indemnity is real money to show: a Paid check, or a "Verified"
      // (indemnity confirmed, check not cleared). An in-adjustment / Pending DETERMINATION
      // (e.g. a NAU "D") is the AIP's calculated figure, not a settlement — show "—" until it
      // clears, so we never display a dollar amount NAU itself hasn't finalized.
      g.confirmed += (c.status === "Verified" || c.status === "Paid") ? (c.indemnityAmount || 0) : 0;
      if ((c.indemnityAmount || 0) > (g.rep.indemnityAmount || 0)) g.rep = c;
    }
    return [...m.values()];
  }, [filtered]);

  // Tab counts respect every filter EXCEPT the status sub-filter and the tab
  // split itself, so the pills always show how many active vs paid claims match
  // the current farmer/crop/year/aip/type selection.
  const tabBase = useMemo(() => {
    let r = allClaims;
    if (filterAIP !== "all") r = r.filter(c => c.aip === filterAIP);
    if (filterType !== "all") r = r.filter(c => (c.policyType || "crop") === filterType);
    if (filterStage !== "all") r = r.filter(c => claimStageLabels(c).includes(filterStage));
    if (filterAdjuster !== "all") r = r.filter(c => cleanAdjuster(c.adjuster) === filterAdjuster);
    if (effYear !== "all") r = r.filter(c => String(c.cropYear || "") === effYear);
    if (filterCrop !== "all") r = r.filter(c => claimCropList(c).includes(filterCrop));
    if (filterFarmer.trim()) { const q = filterFarmer.trim().toUpperCase(); r = r.filter(c => (c.farmerName || "").toUpperCase().includes(q)); }
    return r;
  }, [allClaims, filterAIP, filterType, filterStage, filterAdjuster, effYear, filterCrop, filterFarmer]);
  // Counts are per-CLAIM (distinct group keys), matching the grouped table —
  // so the tab pills and KPIs never say "3" where the table shows one claim.
  const activeCount = countClaims(tabBase.filter(c => isActiveStatus(c.status)));
  const paidCount   = countClaims(tabBase.filter(c => isPaidStatus(c.status)));
  // ⚠ SET ASIDE IS NOT DELETED, AND THE PAGE SAYS SO. A claim the carrier closed with no
  // payment belongs on neither tab — but 66 rows leaving a screen with nothing to explain
  // them is the silent drop this repo forbids everywhere else. The count is named, with
  // the statuses behind it, so "where did Released go" has an answer on the page.
  const settledNoPay = tabBase.filter(c => !isPaidStatus(c.status) && !isActiveStatus(c.status));
  const settledNoPayCount = countClaims(settledNoPay);
  const settledNoPayWords = [...new Set(settledNoPay.map(c => c.status).filter(Boolean))].sort().join(" / ");

  const totalIndemnity = filtered.reduce((s, c) => s + ((c.status === "Verified" || c.status === "Paid") ? (c.indemnityAmount || 0) : 0), 0);
  const openNoDollarCount = grouped.filter(g => g.total === 0).length;
  const aips = [...new Set(allClaims.map(c => c.aip).filter(Boolean))];
  const policyTypes = [...new Set(allClaims.map(c => c.policyType || "crop"))];
  // Status options reflect only what's present in the CURRENT tab (active
  // statuses won't appear on the Paid tab and vice-versa).
  const statuses = [...new Set(tabBase.filter(c => tab === "paid" ? isPaidStatus(c.status) : !isPaidStatus(c.status)).map(c => c.status).filter(Boolean))];
  // Atomic crops, never the joined combos — see claimCropList.
  const crops = [...new Set(allClaims.flatMap(claimCropList))].sort();
  // Stage/claim-type options, derived from the grouped labels present on the
  // claims in the current tab. Scoped by tab only (not tabBase) so selecting a
  // stage doesn't collapse the dropdown to that one option.
  const stages = [...new Set(
    allClaims.filter(c => tab === "paid" ? isPaidStatus(c.status) : !isPaidStatus(c.status)).flatMap(claimStageLabels)
  )].sort();
  // Adjuster options, scoped to the current tab (same pattern as stages).
  const adjusters = [...new Set(
    allClaims.filter(c => tab === "paid" ? isPaidStatus(c.status) : !isPaidStatus(c.status)).map(c => cleanAdjuster(c.adjuster)).filter(Boolean)
  )].sort();
  const farmersWithIndemnities = [...new Set(filtered.map(c => c.farmerName))].length;

  // Status pill styling, shared by mobile + desktop. Paid = cleared check
  // (green). Verified = AIP confirmed indemnity, check not cleared (confident
  // blue — real money on the way). Adjustment = quality ride-along (amber).
  // Open = NAU adjuster assigned, no $ yet (orange). Pending = R&H loss header,
  // no disbursement (orange).
  // A claim is routinely open across SEVERAL counties — and several CROPS — at
  // once. R L JEFFRES' CY2026 corn is prevent-plant in Genesee, Livingston AND
  // Wyoming under one claim number, with a separate Notice of Loss filed for
  // each (M13 P58); Marquart Bros' 3600118 is one prevent-plant claim over corn
  // AND potatoes. Stack them one per line: Crop and County are each 9% of a
  // fixed-layout table, so a comma list renders as "LIVINGST/ON" broken
  // mid-word. One renderer, used by both columns — the two cells are the same
  // shape (a list the AIP folded onto one claim) and had no business drifting.
  const stackedCell = (value) => {
    const parts = String(value || "").split(",").map(x => x.trim()).filter(Boolean);
    if (!parts.length) return "—";
    if (parts.length === 1) return parts[0];
    return (
      <span title={parts.join(", ")}>
        {parts.map((p, pi) => (
          <span key={pi} style={{ display:"block", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p}</span>
        ))}
      </span>
    );
  };

  const statusPill = (s) => {
    const styles = s === "Paid" ? { bg:"#e8f6ec", fg:"#2d6a2d" }
      : s === "Verified" ? { bg:"#e7f0fa", fg:"#1a4a7a" }
      : s === "Adjustment" ? { bg:"#fdf6e7", fg:"#7a5b00" }
      : s === "Reserve" ? { bg:"#eef0f2", fg:"#5a6470" }   // AIP ledger/reserve — not a payment
      : s === "Closed" ? { bg:"#eef0f2", fg:"#5a6470" }    // resolved, no indemnity (prior-year notice)
      : s === "Open" ? { bg:"#fdf1e8", fg:"#b34a10" }
      : { bg:"#fdf1e8", fg:"#c86030" };
    return <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:700, whiteSpace:"nowrap", background: styles.bg, color: styles.fg }}>{s}</span>;
  };

  // ── WHERE A CLAIM IS, AS FOUR STEPS — for the customer's own cards ─────────
  // Filed → Adjusting → Confirmed → Paid. A claim settled with no payment ends on
  // "Closed" instead of "Paid", and a status with no clear place on that line
  // (an AIP ledger "Reserve", or anything this map has never seen) gets NO bar:
  // a guessed position on a claim is worse than none.
  const CLAIM_STEP_OF = { "Notice Filed": 0, "Filed": 0, "Open": 1, "Adjustment": 1, "In Adjustment": 1,
                          "Pending": 1, "Verified": 2, "Paid": 3, "Closed": 3, "Released": 3 };
  const claimProgress = (s) => {
    const at = CLAIM_STEP_OF[s];
    if (at == null) return null;
    const last = (s === "Closed" || s === "Released") ? "Closed" : "Paid";
    const steps = ["Filed", "Adjusting", "Confirmed", last];
    return (
      <div aria-label={`Claim progress: ${steps[at]}`} style={{ display: "flex", gap: 4, marginTop: 10 }}>
        {steps.map((lb, i) => (
          <div key={lb} style={{ flex: "1 1 0", minWidth: 0 }}>
            <div style={{ height: 5, borderRadius: 3, background: i <= at ? (last === "Closed" && i === 3 ? "#8a948a" : "#2d6a2d") : "#e2eae2" }} />
            <div style={{ fontSize: 11, fontWeight: i === at ? 800 : 500, color: i === at ? "#1a2e1a" : "#677167", marginTop: 4 }}>{lb}</div>
          </div>
        ))}
      </div>
    );
  };

  const tabBtn = (key, label, count, accent) => (
    <button onClick={() => { setTab(key); setFilterStatus("all"); setFilterStage("all"); setFilterAdjuster("all"); }}
      style={{
        flex:isMobile?"1 1 0":"0 0 auto", padding:isMobile?"10px 8px":"10px 18px", border:"none", cursor:"pointer",
        background: tab===key ? "#fff" : "transparent", color: tab===key ? "#1a2e1a" : "#60735f",
        fontWeight:800, fontSize:isMobile?12.5:13.5, borderBottom: tab===key ? `3px solid ${accent}` : "3px solid transparent",
        borderRadius:"8px 8px 0 0", display:"flex", alignItems:"center", justifyContent:"center", gap:7, whiteSpace:"nowrap",
      }}>
      {label}
      {count != null && <span style={{ fontVariantNumeric: "tabular-nums", fontSize:11.5, fontWeight:800, padding:"1px 7px", borderRadius:10, background: tab===key ? accent : "#e4ece4", color: tab===key ? "#fff" : "#60735f" }}>{count}</span>}
    </button>
  );

  // Claim Check tab — a forward-looking "do we need to open a claim?" screener. Placed
  // before the empty-claims guard so it's reachable even for a farm/book with no claims.
  // ⚠ WRAPPED, NOT SWIPED. This strip already had `overflowX: auto`, so it never
  // dragged the page — but on a 390px phone "🔍 Check" ended at 448px, which means
  // the cross-check tool sat off the right edge with nothing on screen to say it was
  // there. A scroll container keeps the page still and hides the feature; wrapping
  // does neither. Measured on production 2026-09-24.
  const claimsTabBar = (
    <div style={{ display:"flex", flexWrap:"wrap", gap:isMobile?2:4, borderBottom:"1px solid #e4ece4", marginBottom:16 }}>
      {showManager && tabBtn("check", isMobile ? "🗂️ Manager" : "🗂️ Claim Manager", null, "#1a4a7a")}
      {/* Filed → in adjustment → paid, left to right. The count is null until the
          mirror has actually been read: a 0 that means "not loaded yet" is the
          confident-zero this lane exists to avoid. */}
      {tabBtn("active", isMobile ? "🔴 Active" : "🔴 Active Claims", activeCount, "#c86030")}
      {tabBtn("paid", isMobile ? "✅ Paid" : "✅ Paid Indemnities", paidCount, "#2d6a2d")}
      {/* Kevin, 2026-09-23: "we have had AIPs make mistakes and we want to be able to catch
          those… we just need to show when a discrepancy exists and let us figure it out."
          Agent-only: it reads the carrier's own claim worksheet and says where that
          worksheet disagrees with itself. No count until it has loaded — a 0 that means
          "not read yet" is the confident-zero the tab beside it already warns about. */}
      {showManager && tabBtn("crosscheck", isMobile ? "🔍 Check" : "🔍 Cross-check", null, "#8a3324")}
    </div>
  );
  // Sits under the tabs, quiet, and only when there is something to say.
  const settledNote = settledNoPayCount > 0 ? (
    <div style={{ fontSize: 11, color: "#60735f", margin: "-10px 0 14px" }}>
      {settledNoPayCount} claim{settledNoPayCount === 1 ? "" : "s"} settled with no payment
      {settledNoPayWords ? ` (${settledNoPayWords})` : ""} — on neither tab, still on the farmer's card.
    </div>
  ) : null;
  // ⚠ NOT A TAB OF ITS OWN. Kevin, 2026-09-09: "notices filed tab is useless, it
  // only has NAU customers and its an unneccesary tab. All that information should
  // be in active claims or claims manager somehow."
  //
  // He is right on both counts, and the first one is CARRIER BEHAVIOUR rather than a
  // defect — which is worth writing down, because it looks exactly like a broken
  // filter. Measured over the CY2026 P58 mirror: R&H files 72 noticed policy x
  // commodity cells and already carries a claim row for EVERY one of them (ZERO
  // outstanding), while NAU files 126 and leaves 24 unstaged. Nothing is being
  // dropped on the way in — all 149 R&H notice rows across 52 policies join cleanly,
  // 0 orphaned — R&H simply writes a stage record where NAU does not. So a tab that
  // can only ever show one carrier is a tab that reads as a bug.
  //
  // ⚠ AND IT IS A BAND ABOVE THE TABLE, NOT ROWS INSIDE IT. `grouped` feeds the
  // counts, the totals, the sort and the CSV export; a synthetic notice row injected
  // there would carry $0 into a money column, which is precisely the fabricated-claim
  // failure BUG-036 exists to prevent. These carry no dollars and can reach no total.

  if (tab === "check" && showManager) {
    return (
      <div style={{ padding:isMobile?16:32 }}>
        <h2 style={{ fontFamily:"'Lora',serif", color:"#1a2e1a", margin:"0 0 14px" }}>Claims</h2>
        {claimsTabBar}{settledNote}
        <ClaimManagerView farmers={farmers} isMobile={isMobile} restrictFarmerIds={restrictFarmerIds} />
      </div>
    );
  }

  if (tab === "crosscheck" && showManager) {
    return (
      <div style={{ padding:isMobile?16:32 }}>
        <h2 style={{ fontFamily:"'Lora',serif", color:"#1a2e1a", margin:"0 0 14px" }}>Claims</h2>
        {claimsTabBar}{settledNote}
        <ClaimCrossCheckPanel isMobile={isMobile} restrictFarmerIds={restrictFarmerIds} />
      </div>
    );
  }

  if (allClaims.length === 0) {
    return (
      <div style={{ padding:isMobile?16:32 }}>
        <h2 style={{ fontFamily:"'Lora',serif", color:"#1a2e1a", margin:"0 0 16px" }}>Claims</h2>
        {claimsTabBar}{settledNote}
        <Card style={{ textAlign:"center", padding:"48px 24px", border:"1px dashed #c8d8c8" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>💰</div>
          <div style={{ fontWeight:800, fontSize:17, color:"#1a2e1a", marginBottom:8 }}>No Claims Yet</div>
          <div style={{ color:"#60735f", fontSize:13, maxWidth:400, margin:"0 auto" }}>
            {isPortal ? "Your claims will show here once your insurance company opens them." : "Claims from M13 P20/P20A/P20X records will appear here once processed by the daily sync."}{!isPortal && <> Use <strong>🗂️ Claim Manager</strong> above to work the whole book — roster every farmer with coverage, track who you've contacted, and flag anyone under guarantee with no claim opened.</>}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding:isMobile?16:32 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, margin:"0 0 14px" }}>
        <h2 style={{ fontFamily:"'Lora',serif", color:"#1a2e1a", margin:0 }}>Claims</h2>
        {!isPortal && (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
          {isCurrentUserAdmin() && (
          <button onClick={refreshPriorYear} disabled={refreshing}
            style={{ padding:"8px 14px", background: refreshing ? "#9ab39a" : "#2d6a2d", color:"#fff", border:"none", borderRadius:7, cursor: refreshing ? "default" : "pointer", fontWeight:700, fontSize:12.5 }}>
            {refreshing ? "Refreshing..." : "🔄 Refresh prior-year payments"}
          </button>
          )}
          <span style={{ fontSize:10.5, color:"#60735f" }}>
            {refreshMsg || "Daily cron at 13:00 UTC. Click to pull new CY25 ECO/SCO settlements immediately."}
          </span>
        </div>
        )}
      </div>

      {/* Active / Paid / Claim Check tabs */}
      {claimsTabBar}{settledNote}

      {/* Does what we display still equal what the AIP said? Silent when it
          does; loud the day a parse change drops or double-counts a payment. */}
      {!isPortal && m13TieOut.checked > 0 && (
        m13TieOut.off === 0 ? (
          <div style={{ fontSize:11, color:"#2d6a2d", margin:"-4px 0 12px", fontWeight:600 }}
            title={`Every disbursement shown sums to the AIP's own stated claim total (M13 P20).${m13TieOut.unverifiable ? `\n\n${m13TieOut.unverifiable} claims can't be checked this way — R&H P20 carries no total field.` : ""}`}>
            ✓ Ties to M13 — {m13TieOut.checked.toLocaleString()} claims reconcile to the AIP's stated total
            {m13TieOut.unverifiable > 0 && <span style={{ color:"#60735f", fontWeight:500 }}> · {m13TieOut.unverifiable.toLocaleString()} not verifiable (R&H states no claim total)</span>}
          </div>
        ) : (
          <div style={{ fontSize:12, color:"#8a2020", background:"#fdecec", border:"1px solid #f3c9c9", borderRadius:8, padding:"10px 12px", margin:"0 0 14px", fontWeight:700 }}>
            ⚠ {m13TieOut.off} of {m13TieOut.checked} claims do NOT reconcile to the AIP's stated total
            (variance {m13TieOut.variance < 0 ? "−" : "+"}${Math.abs(m13TieOut.variance).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}).
            <span style={{ fontWeight:500 }}> Displayed amounts may be missing or double-counting a disbursement — check the sync before quoting these figures.</span>
          </div>
        )
      )}

      {/* ⚠ A MIRROR WE COULD NOT READ IS NOT "NOTHING OUTSTANDING". The notices are
          ordinary claim rows now, so a failed read simply produces no rows — which is
          exactly what a quiet book looks like. This is the one state that has to speak
          for itself. */}
      {tab === "active" && noticeErr && (
        <div style={{ fontSize:12.5, fontWeight:700, color:"#8a2020", background:"#fdecec",
                      border:"1px solid #f3c9c9", borderRadius:8, padding:"9px 12px", marginBottom:12 }}>
          ⚠ Could not read the Notice of Loss mirror — {noticeErr}. Filed notices are missing from
          this list. This is <b>not</b> "no notices outstanding".
        </div>
      )}

      {/* The one-line purpose stays visible; the status vocabulary and sourcing
          notes are one click away rather than four lines above the table. */}
      {tab === "active" && (
        <details style={{ fontSize:12, color:"#60735f", margin:"-4px 0 14px", lineHeight:1.5 }}>
          <summary style={{ cursor:"pointer" }}>Claims filed and in adjustment · <span style={{ textDecoration:"underline" }}>what the statuses mean</span></summary>
          Claims filed and in adjustment — surfaced from NAU P20X stage records the morning after they're opened.
          <strong style={{ color:"#1a2e1a" }}> Open</strong> = adjuster assigned, no payment yet ·
          <strong style={{ color:"#1a4a7a" }}> Verified</strong> = indemnity confirmed, check not cleared.
          <br />
          County on an open claim is every county the grower filed a Notice of Loss in for that crop (M13 P58) — one claim
          number often spans several. Acres stay blank until the adjuster keys them.
          <br />
          <strong style={{ color: "#7a5b00" }}>Notice Filed</strong> = the grower has filed a Notice of Loss (M13 P58) and the
          AIP has not written a stage record yet, so there is no indemnity row and no dollar figure — the claim is open all the
          same. In practice these are NAU policies; R&amp;H stages every notice it takes.
        </details>
      )}

      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":(customerView ? "repeat(3,1fr)" : "repeat(4,1fr)"), gap:12, marginBottom:20 }}>
        {(tab === "active" ? [
          { label:"Active Claims", value:grouped.length, color:"#c86030" },
          { label:"Open · No $ Yet", value:openNoDollarCount, color:"#1a4a7a" },
          { label:"Confirmed $ (awaiting check)", value:`$${totalIndemnity.toLocaleString(undefined,{minimumFractionDigits:2})}`, color:"#6b4f00" },
          { label:"Farmers", value:farmersWithIndemnities, color:"#1a2e1a" },
        ] : [
          { label:"Paid Claims", value:grouped.length, color:"#1a2e1a" },
          { label:"Total Indemnity", value:`$${totalIndemnity.toLocaleString(undefined,{minimumFractionDigits:2})}`, color:"#c04030" },
          { label:"Farmers", value:farmersWithIndemnities, color:"#1a4a7a" },
          { label:"Policy Types", value:policyTypes.map(t=>t.toUpperCase()).join(", "), color:"#6b4f00" },
        ]).filter((s) => !(customerView && s.label === "Farmers")).map((s,i) => (
          <Card key={i} style={{ textAlign:"center", padding:isMobile?"12px 8px":"16px" }}>
            <div style={{ fontSize:10, color:"#60735f", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>{s.label}</div>
            <div style={{ fontSize:isMobile?18:22, fontWeight:800, color:s.color, fontVariantNumeric: "tabular-nums" }}>{s.value}</div>
          </Card>
        ))}
      </div>

      <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16, fontSize:12, alignItems:"center" }}>
        {!customerView && <div style={{ position:"relative", flex:isMobile ? "1 1 100%" : "0 0 auto" }}>
          <input
            type="text"
            value={filterFarmer}
            onChange={e => setFilterFarmer(e.target.value)}
            placeholder="🔍 Search farmer..."
            style={{ padding:"7px 28px 7px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12, width:isMobile ? "100%" : 220, fontFamily:"inherit", boxSizing:"border-box" }}
          />
          {filterFarmer && (
            <button onClick={() => setFilterFarmer("")} title="Clear search"
              style={{ position:"absolute", right:4, top:"50%", transform:"translateY(-50%)", border:"none", background:"transparent", color:"#60735f", cursor:"pointer", fontSize:14, padding:"2px 6px", lineHeight:1 }}>
              ✕
            </button>
          )}
        </div>}
        <select value={effYear} onChange={e => setFilterYear(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12 }}>
          <option value="all">All Years</option>
          {years.map(y => <option key={y} value={String(y)}>CY{String(y).slice(2)} ({y})</option>)}
        </select>
        <select value={filterCrop} onChange={e => setFilterCrop(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12 }}>
          <option value="all">All Crops</option>
          {crops.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12 }}>
          <option value="all">All Types</option>
          {policyTypes.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
        </select>
        {!customerView && <select value={filterAIP} onChange={e => setFilterAIP(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12 }}>
          <option value="all">All AIPs</option>
          {aips.map(a => <option key={a} value={a}>{aipLabel(a)}</option>)}
        </select>}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12 }}>
          <option value="all">All Statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {stages.length > 0 && (
          <select value={filterStage} onChange={e => setFilterStage(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12 }}>
            <option value="all">All Stages</option>
            {stages.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {adjusters.length > 0 && (
          <select value={filterAdjuster} onChange={e => setFilterAdjuster(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12 }}>
            <option value="all">All Adjusters</option>
            {adjusters.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #dde5dd", fontSize:12 }}>
          <option value="date">Sort by Date</option>
          <option value="amount">Sort by Amount</option>
          {!customerView && <option value="farmer">Sort by Farmer</option>}
        </select>
      </div>

      {filtered.length === 0 ? (
        <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #c8d8c8" }}>
          <div style={{ fontSize:32, marginBottom:10 }}>{tab === "active" ? "🌱" : "💰"}</div>
          <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:6 }}>
            {tab === "active" ? "No active claims match these filters" : "No paid indemnities match these filters"}
          </div>
          <div style={{ color:"#60735f", fontSize:12.5, maxWidth:420, margin:"0 auto" }}>
            {tab === "active"
              ? "Active claims (Open / Verified / Pending) appear here the morning after a claim is filed — currently NAU only."
              : "Cleared-check indemnity payments from M13 P20/P20A records appear here."}
          </div>
        </Card>
      ) : (isMobile || customerView) ? (
        // Cards on a phone, and for a grower on any width: two columns on a desktop.
        <div style={isMobile ? { display:"flex", flexDirection:"column", gap:10 }
                             : { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(380px, 100%), 1fr))", gap:14 }}>
          {grouped.map((grp, i) => {
            const c = grp.rep;   // largest disbursement drives the card fields
            // Stage codes are an MPCI (crop) concept. PRF/DRP/LRP don't have
            // field/area stages — their FL code is NOT ECO/SCO, so don't
            // translate it (PRF has no ECO). Suppress stage for non-crop.
            const stageParts = (c.policyType && c.policyType !== "crop") ? null : formatStageParts(c.claimStages, c.aip);
            const adjc = adjusterCell(c);
            // Same confirmed-only figure the desktop table prints — a phone and a
            // laptop showing different totals for one claim is its own bug.
            const shownAmt = grp.confirmed || 0;
            const hasMoney = shownAmt > 0;
            const mix = claimPayMix(grp.rows);
            const showEscrow = !hasMoney && mix.escrow > 0;   // ECO/SCO awaiting funding
            return (
            <Card key={i} style={{ borderLeft:`4px solid ${c.status === "Paid" ? "#2d6a2d" : "#c86030"}`, cursor: customerView ? "default" : "pointer" }}
              onClick={() => onSelectFarmer && onSelectFarmer(c.farmerObj, c)}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6, gap:8 }}>
                <span style={{ fontWeight:800, fontSize:13, color:"#1a2e1a" }}>{c.farmerName}</span>
                {hasMoney || showEscrow
                  ? <span style={{ textAlign:"right" }} title={mix.title}>
                      <span style={{ fontVariantNumeric: "tabular-nums", fontWeight:700, color: hasMoney ? "#c04030" : "#9a8270", fontSize:14 }}>
                        ${(hasMoney ? shownAmt : mix.escrow).toLocaleString(undefined,{minimumFractionDigits:2})}
                      </span>
                      <div style={{ fontSize:10, fontWeight:700, color:"#7a5b00", textTransform:"uppercase", letterSpacing:"0.04em" }}>{mix.label || "escrow · awaiting funding"}</div>
                    </span>
                  : statusPill(c.status)}
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", fontSize:11, color:"#60735f", alignItems:"center" }}>
                {c.crop && <span style={{ fontWeight:700, color:"#1a2e1a" }}>{c.crop}{c.cropYear ? ` ’${String(c.cropYear).slice(2)}` : ""}</span>}
                {c.county && <><span>•</span><span>{c.county}</span></>}
                {stageParts && <><span>•</span><span style={{ fontWeight:700 }}>{renderStageCell(c)}</span></>}
                {c.lossAcres != null && <><span>•</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.lossAcres,1)} ac</span></>}
                {c.policyType && c.policyType !== "crop" && <><span>•</span><span>{c.policyType.toUpperCase()}</span></>}
                {/* ⚠ LABELLED, AND NEVER BLANK. On a phone the name sat unlabelled in a
                    dot run with no column header to say what it was, which is the half of
                    Kevin’s report that was a layout problem; the reason is the half that
                    was a data problem. */}
                <span>•</span>
                <span title={adjc.why || undefined}>
                  {/* The "ADJ" label exists because a bare surname in a dot run has
                      nothing to say what it is. A REASON now says so itself — "adjuster
                      not assigned yet" — so prefixing it would read "ADJ adjuster not
                      assigned yet". The label rides with the name only. */}
                  {adjc.name
                    ? <><span style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.04em", color:"#9ab098" }}>adj </span>
                        <span style={{ color:"#1a2e1a", fontWeight:600 }}>{adjc.name}</span></>
                    : <span style={{ color:"#9ab098", fontStyle:"italic" }}>{adjc.reason}</span>}
                </span>
                <span>•</span>
                <span>{claimDateCell(c, "—")}</span>
                <span>•</span>
                <Pill color={aipColor(c.aip)}>{aipLabel(c.aip)}</Pill>
                {(hasMoney || showEscrow) && <>{statusPill(c.status)}</>}
              </div>
              {customerView && claimProgress(c.status)}
            </Card>
          );})}
        </div>
      ) : (
        <Card style={{ padding:0, overflow:"hidden" }}>
          <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
            <colgroup>
              {(tab === "active"
                ? ["15%","8%","9%","9%","10%","6%","7%","5%","11%","7%","6%","7%"]
                : ["15%","10%","9%","9%","11%","7%","8%","6%","9%","8%","8%"]
              ).map((w,wi) => <col key={wi} style={{ width:w }} />)}
            </colgroup>
            <thead>
              <tr style={{ background:"#f4f8f4" }}>
                {(tab === "active"
                  ? ["Farmer","Date","Crop","County","Stage","Acres","Type","CY","Adjuster","AIP","Status","Amount"]
                  : ["Farmer","Check Date","Crop","County","Stage","Acres","Type","CY","Policy","AIP","Amount"]
                ).map(h => (
                  <th key={h} style={{ padding:"7px 7px", textAlign:(h==="Amount"||h==="Acres")?"right":"left", fontSize:10.5, fontWeight:800, letterSpacing:"0.04em", color:"#60735f", textTransform:"uppercase", borderBottom:"1px solid #e4ece4" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.map((grp, i) => {
                const c = grp.rep;   // largest disbursement drives the row fields
                const adjc = adjusterCell(c);
                const typeCell = (
                  <Pill color={c.policyType === "lrp" ? "#7a3b10" : c.policyType === "drp" ? "#1a4a7a" : c.policyType === "prf" ? "#3a3a7a" : "#2d4a2d"}>
                    {(c.policyType||"crop").toUpperCase()}
                  </Pill>
                );
                // Amount = the claim total, broken down by HOW it was paid. Every
                // settled row carries the caption — "check", "premium credit",
                // "$1,589 check + $43,398 credit" — so a blank line never has to be
                // read as "single plain check" by inference (it used to mean exactly
                // that, and nothing said so).
                // Show only the CONFIRMED amount (Paid check / Verified). A Pending
                // determination reads "—" until it settles — never a figure NAU hasn't finalized.
                const shownAmt = grp.confirmed || 0;
                const mix = claimPayMix(grp.rows);
                // Nothing settled but an ESCROW on the claim: show the escrowed
                // figure muted rather than "—". It is confirmed money awaiting
                // funding, and hiding it reads as "no money". NOT labelled
                // ECO/SCO — that attribution was never sourced and is wrong on 4
                // of the 9 D rows in the book. See CLAIM_PAY_METHOD.
                const showEscrow = shownAmt <= 0 && mix.escrow > 0;
                const amountCell = (
                  <td style={{ padding:"8px 7px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontWeight:700, color: shownAmt > 0 ? "#c04030" : showEscrow ? "#9a8270" : "#bbb", fontSize:12.5, wordBreak:"break-word" }} title={mix.title}>
                    {shownAmt > 0 ? `$${shownAmt.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}`
                      : showEscrow ? `$${mix.escrow.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}` : "—"}
                    {(mix.label || showEscrow) && <div style={{ fontSize:10, fontWeight:700, color:"#7a5b00", fontFamily:"inherit", textTransform:"uppercase", letterSpacing:"0.04em" }}>{mix.label || "escrow · awaiting funding"}</div>}
                  </td>
                );
                return (
                <tr key={i} style={{ borderBottom:"1px solid #f0f4f0", cursor:"pointer" }}
                  onClick={() => onSelectFarmer && onSelectFarmer(c.farmerObj, c)}>
                  <td style={{ padding:"8px 7px", fontWeight:700, color:"#1a2e1a", wordBreak:"break-word" }}>{c.farmerName}</td>
                  <td style={{ padding:"8px 7px", fontVariantNumeric: "tabular-nums", fontSize:11, color:(c.checkDate||c.claimDate)?"#1a2e1a":"#bbb" }}>{claimDateCell(c, "—")}</td>
                  <td style={{ padding:"8px 7px", color:"#1a2e1a", fontWeight:600, wordBreak:"break-word" }}>{stackedCell(c.crop)}</td>
                  <td style={{ padding:"8px 7px", color:"#4a5a4a", wordBreak:"break-word" }}>{stackedCell(c.county)}</td>
                  <td style={{ padding:"8px 7px", wordBreak:"break-word" }}>{renderStageCell(c)}</td>
                  <td style={{ padding:"8px 7px", textAlign:"right", fontVariantNumeric: "tabular-nums", color: c.lossAcres != null ? "#5a7a5a" : "#bbb" }}>{c.lossAcres != null ? `${fmt(c.lossAcres,1)}` : "—"}</td>
                  <td style={{ padding:"8px 7px" }}>{typeCell}</td>
                  <td style={{ padding:"8px 7px", fontVariantNumeric: "tabular-nums", color:"#60735f" }}>{c.cropYear ? `'${String(c.cropYear).slice(2)}` : "—"}</td>
                  {/* A dash says "no data" and nothing more. Four different facts reach
                      this cell; each names itself, with the mechanism on hover. */}
                  {tab === "active" && <td style={{ padding:"8px 7px", color: adjc.name ? "#4a5a4a" : "#a8b8a6", fontStyle: adjc.name ? "normal" : "italic", wordBreak:"break-word" }} title={adjc.why || undefined}>{adjc.name || adjc.reason}</td>}
                  {tab === "paid" && <td style={{ padding:"8px 7px", color:"#60735f", whiteSpace:"nowrap" }}>{c.policyNumber}</td>}
                  <td style={{ padding:"8px 7px" }}><Pill color={aipColor(c.aip)}>{aipLabel(c.aip)}</Pill></td>
                  {tab === "active" && <td style={{ padding:"8px 7px" }}>{statusPill(c.status)}</td>}
                  {amountCell}
                </tr>
              );})}
            </tbody>
          </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SYNC LOG SCREEN — Morning dashboard showing pipeline health + recent changes
// Rewritten Apr 2026 to add:
//   • Pipeline Health panel (all 7 pipelines, business-day aware staleness)
//   • Sync Runs table (actual cron executions from m13_import_log)
//   • Activity Feed with re-import dedup (default ON)
// ─────────────────────────────────────────────────────────────────────────────

// Business-day-aware hours since a timestamp.
// Weekends count as 0 hours (Friday 5pm → Monday 9am = ~16 business hours, not ~64).
function businessHoursSince(iso) {
  if (!iso) return Infinity;
  const then = new Date(iso);
  const now = new Date();
  let hours = 0;
  const cursor = new Date(then);
  while (cursor < now) {
    const day = cursor.getDay(); // 0=Sun, 6=Sat
    if (day !== 0 && day !== 6) hours += 1;
    cursor.setHours(cursor.getHours() + 1);
  }
  return hours;
}

// Simple wall-clock hours (non-business-day aware) for pipelines that should run every calendar day
function wallHoursSince(iso) {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / 1000 / 3600;
}

// Days since (wall-clock)
function daysSince(iso) {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / 1000 / 3600 / 24;
}

// ─────────────────────────────────────────────────────────────────────────────
// Historical Backfill Admin — Sync Log section that lets the user re-run
// CY2025 backfills with the current parser. The deployed backfill endpoints
// accept either a DEBUG_AUTH bearer (CLI path) or a valid Supabase session
// JWT (this dashboard path). We pass the user's existing access token from
// localStorage so no DEBUG_AUTH dance is needed.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// FORCE TODAY'S SYNC — re-run the current-year M13 + NAU pull immediately.
//
// The daily cron has an idempotency guard: once a file has imported today, every
// later attempt is a no-op. That is what makes the 3x-daily retry pattern safe,
// but it also means a PARSER FIX deployed at noon does not reach the data until
// tomorrow. ?force=1 bypasses the guard, and it is admin-gated, so it needs the
// signed-in session token exactly like the backfill below.
//
// Added 2026-08-01 after the grain/silage APH fix: the correction existed in
// code but the wrong yields sat in the database with no in-app way to refresh.
function ForceSyncNow({ isMobile }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);

  async function run() {
    setBusy(true);
    setMsg("Re-running R&H (M13)…");
    try {
      const token = localStorage.getItem("cloverag_access_token");
      if (!token) throw new Error("Not signed in — log out and back in, then retry.");
      const headers = { Authorization: `Bearer ${token}` };

      const rh = await fetch("/api/m13-sync?force=1", { method: "POST", headers });
      const rhJson = await rh.json().catch(() => ({}));
      if (!rh.ok) throw new Error(`R&H failed: ${rhJson.error || rh.status}`);
      const rhCov = rhJson.imported?.cropCoverage ?? rhJson.cropCoverage ?? "?";
      const rhUnits = rhJson.imported?.cropCoverageUnits ?? rhJson.cropCoverageUnits ?? 0;

      setMsg(`R&H ✅ ${rhCov} coverages, ${rhUnits} units. Re-running NAU…`);
      const nau = await fetch("/api/nau-sync?force=1", { method: "POST", headers });
      const nauJson = await nau.json().catch(() => ({}));
      if (!nau.ok) throw new Error(`NAU failed: ${nauJson.error || nau.status}`);
      const nauCov = nauJson.imported?.cropCoverage ?? nauJson.cropCoverage ?? "?";
      const nauUnits = nauJson.imported?.cropCoverageUnits ?? nauJson.cropCoverageUnits ?? 0;

      setMsg(`✅ R&H ${rhCov} + NAU ${nauCov} coverages · ${rhUnits + nauUnits} unit rows. Reloading…`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
    setBusy(false);
  }

  return (
    <div style={{ ...uiCard(), marginBottom: 14 }}>
      <div style={{ ...uiLabel, marginBottom: 4 }}>FORCE TODAY'S SYNC</div>
      <div style={{ fontSize: 12.5, color: T.body, lineHeight: 1.6, marginBottom: 10, maxWidth: 720 }}>
        Re-runs the current-year R&amp;H and NAU files now, bypassing the once-a-day guard.
        Use after a parser fix ships — otherwise corrected values wait until tomorrow's cron.
        Safe to run anytime: each sync rewrites its own rows.
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={run} disabled={busy}
          style={{ fontSize: 13, fontWeight: 700, padding: "8px 16px", borderRadius: 8,
                   cursor: busy ? "default" : "pointer", border: "none",
                   background: busy ? "#c8d6c8" : T.green, color: "#fff", fontFamily: "inherit" }}>
          {busy ? "Running…" : "Force re-sync now"}
        </button>
        {msg && <span style={{ fontSize: 12.5, color: msg.startsWith("❌") ? T.danger : T.body }}>{msg}</span>}
      </div>
    </div>
  );
}

function HistoricalBackfillAdmin({ isMobile }) {
  const [running, setRunning] = useState(false);
  const [status, setStatus]   = useState(null);   // { phase: 'nau'|'rh'|'done', nauResult, rhResult, error }
  const [confirming, setConfirming] = useState(false);

  async function runBackfill() {
    setRunning(true);
    setStatus({ phase: 'nau' });
    try {
      // Pull the user's Supabase access token (set on login by sbAuth).
      const token = localStorage.getItem("cloverag_access_token");
      if (!token) {
        throw new Error("Not signed in — please log out and back in, then try again.");
      }
      const headers = { "Authorization": `Bearer ${token}` };

      // 1. NAU backfill (writing-agent filter restricts to Kevin's iids;
      //    Sullivan + Mike's customers excluded by parser per CLAUDE.md).
      const nauRes = await fetch("/api/nau-sync?backfillYear=2025&fields=1", {
        method: "POST", headers,
      });
      const nauResult = await nauRes.json();
      if (!nauRes.ok) throw new Error(`NAU backfill failed: ${nauResult.error || nauRes.status}`);

      setStatus({ phase: 'rh', nauResult });

      // 2. R&H backfill (no agent filter — file is consolidated and includes
      //    both Kevin's and Mike's customers).
      const rhRes = await fetch("/api/m13-sync?backfillYear=2025&fields=1", {
        method: "POST", headers,
      });
      const rhResult = await rhRes.json();
      if (!rhRes.ok) throw new Error(`R&H backfill failed: ${rhResult.error || rhRes.status}`);

      setStatus({ phase: 'done', nauResult, rhResult });
    } catch(e) {
      setStatus({ phase: 'error', error: e.message });
    }
    setRunning(false);
  }

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", letterSpacing: "0.02em", marginBottom: 10 }}>
        🔧 Historical Backfill
      </div>
      <div style={{ padding: "14px 16px", border: "1px solid #e4ece4", borderRadius: 10, background: "#fafcfa" }}>
        <div style={{ fontSize: 12, color: "#5a7a5a", lineHeight: 1.5, marginBottom: 10 }}>
          Re-runs the CY2025 backfill for both NAU and R&H using the current parser.
          Upgrades historical claim rows to per-(crop, county, stage) rows <b>and</b> writes
          CY2025 per-field crop into <code>field_crop_history</code> (the Field Mapper rotation
          history, via <code>&amp;fields=1</code>). Idempotent — replaces the prior
          <code>M13_2025_backfill</code> rows; today's CY2026 daily syncs are unaffected.
        </div>
        {!confirming && !running && status?.phase !== 'done' && status?.phase !== 'error' && (
          <button
            onClick={() => setConfirming(true)}
            style={{ padding: "8px 16px", borderRadius: 6, border: "1.5px solid #2d5a10", background: "#fff", color: "#2d5a10", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
            Re-run CY2025 Backfill (NAU + R&H)
          </button>
        )}
        {confirming && !running && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#7a4a00", fontWeight: 700 }}>
              This will take ~60–90 seconds. Continue?
            </span>
            <button
              onClick={() => { setConfirming(false); runBackfill(); }}
              style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#2d5a10", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
              Yes, run it
            </button>
            <button
              onClick={() => setConfirming(false)}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", color: "#5a5a5a", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        )}
        {running && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 0" }}>
            <div style={{ width: 14, height: 14, border: "2px solid #2d5a10", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            <span style={{ fontSize: 12, color: "#5a7a5a", fontWeight: 700 }}>
              {status?.phase === 'nau'  ? "Running NAU CY2025 backfill (Kevin's book)…"
               : status?.phase === 'rh' ? "NAU done. Running R&H CY2025 backfill (full book)…"
               : "Starting…"}
            </span>
            <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
          </div>
        )}
        {status?.phase === 'done' && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, color: "#2d5a10" }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#2d5a10" }}>✓ Backfill complete. Refresh a farmer's Claims History to see per-(crop, county, stage) rows.</div>
            <BackfillResultRow label="NAU" result={status.nauResult} />
            <BackfillResultRow label="R&H" result={status.rhResult} />
            <button
              onClick={() => setStatus(null)}
              style={{ alignSelf: "flex-start", marginTop: 4, padding: "4px 10px", borderRadius: 5, border: "1px solid #ddd", background: "#fff", color: "#5a5a5a", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              Dismiss
            </button>
          </div>
        )}
        {status?.phase === 'error' && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 12px", background: "#fef0f0", border: "1px solid #f0c8c8", borderRadius: 6, color: "#9a3030" }}>
            <div style={{ fontWeight: 800, fontSize: 12 }}>✗ Backfill failed</div>
            <div style={{ fontSize: 11, fontVariantNumeric: "tabular-nums" }}>{status.error}</div>
            <button
              onClick={() => setStatus(null)}
              style={{ alignSelf: "flex-start", padding: "4px 10px", borderRadius: 5, border: "1px solid #f0c8c8", background: "#fff", color: "#9a3030", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function BackfillResultRow({ label, result }) {
  if (!result) return null;
  const counts = result.counts || {};
  const wouldWrite = result.would_write || {};
  return (
    <div style={{ fontSize: 11, color: "#5a7a5a", fontVariantNumeric: "tabular-nums" }}>
      <strong style={{ color: "#1a2e1a" }}>{label}:</strong>{" "}
      {result.success ? (
        <>
          {wouldWrite.indemnities != null ? `${wouldWrite.indemnities} indemnities` : ""}
          {wouldWrite.loss_notices != null ? ` · ${wouldWrite.loss_notices} loss notices` : ""}
          {wouldWrite.indemnity_lines != null ? ` · ${wouldWrite.indemnity_lines} line items` : ""}
          {result.year ? ` · CY${result.year}` : ""}
        </>
      ) : "no result"}
    </div>
  );
}

// Admin control: backfill the high-res field-weather grid (wny_grid_weather_daily)
// from gridMET (source goes back to 1979). Finishes the current year (with MRMS
// radar) then loads N prior full years gridMET-only (faster; MRMS only exists for
// recent years). Runs one year per request to stay under the 300s function budget.
// Idempotent (upsert on cell_id,obs_date) — safe to re-run. Powers the Field
// Mapper per-field weather page; more years unlocks year-over-year + vs-normal.
function WeatherBackfillAdmin({ isMobile }) {
  const [depth, setDepth] = useState(5);          // prior full years to load
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [log, setLog] = useState([]);             // [{year, status, rows, msg}]
  const [done, setDone] = useState(false);
  const [catching, setCatching] = useState(false); // "catch up now" (mode=daily)
  const [catchMsg, setCatchMsg] = useState(null);  // {ok, text}

  // Force the nightly observation pull early. Same ?mode=daily the 9 AM ET cron
  // fires — gridMET last 7d + MRMS radar last 3d + a forecast refresh. Idempotent
  // (upsert on cell_id,obs_date), so this is safe to hit any number of times.
  // Exists because after a big rain event yesterday's total sits in the table as
  // FORECAST until the next observation run replaces it with gauge-corrected radar.
  async function catchUp() {
    setCatching(true); setCatchMsg(null);
    const token = localStorage.getItem("cloverag_access_token");
    if (!token) { setCatchMsg({ ok: false, text: "Not signed in — log out and back in, then retry." }); setCatching(false); return; }
    try {
      const res = await fetch(`/api/wny-weather-sync?mode=daily`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const grid = data.rows_upserted || 0;
      const mrms = data.mrms?.rows_upserted || 0;
      const fc = data.forecast?.rows_upserted || 0;
      // The observation window (data.to) is the thing worth reporting — it answers
      // "did yesterday land?", which a bare row count does not.
      setCatchMsg({ ok: true, text: `✓ Observations through ${data.to || "—"} · ${grid.toLocaleString()} gridMET + ${mrms.toLocaleString()} MRMS + ${fc.toLocaleString()} forecast rows` });
    } catch (e) {
      setCatchMsg({ ok: false, text: `✗ ${String(e.message || e).slice(0, 160)}` });
    }
    setCatching(false);
  }

  async function run() {
    setRunning(true); setDone(false); setLog([]);
    const token = localStorage.getItem("cloverag_access_token");
    if (!token) { setLog([{ year: "—", status: "error", msg: "Not signed in — log out and back in, then retry." }]); setRunning(false); setDone(true); return; }
    const headers = { Authorization: `Bearer ${token}` };
    const cur = new Date().getFullYear();
    const todayIso = new Date().toISOString().slice(0, 10);
    // current year keeps MRMS; prior years gridMET-only (mrms=0) for speed
    const jobs = [{ year: cur, from: `${cur}-01-01`, to: todayIso, mrms: true }];
    for (let y = cur - 1; y >= cur - depth; y--) jobs.push({ year: y, from: `${y}-01-01`, to: `${y}-12-31`, mrms: false });
    const results = [];
    for (const j of jobs) {
      results.push({ year: j.year, status: "running" });
      setLog([...results]);
      try {
        const res = await fetch(`/api/wny-weather-sync?mode=backfillDaily&from=${j.from}&to=${j.to}${j.mrms ? "" : "&mrms=0"}`, { method: "POST", headers });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        const rows = (data.rows_upserted || 0) + (data.mrms?.rows_upserted || 0);
        results[results.length - 1] = { year: j.year, status: "done", rows };
      } catch (e) {
        results[results.length - 1] = { year: j.year, status: "error", msg: String(e.message || e).slice(0, 160) };
      }
      setLog([...results]);
    }
    setRunning(false); setDone(true);
  }

  const dot = { running: "#d0954a", done: "#2d6a1a", error: "#9a3030" };
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", letterSpacing: "0.02em", marginBottom: 10 }}>
        🌦 Field-Weather Backfill (high-res grid)
      </div>
      <div style={{ padding: "14px 16px", border: "1px solid #e4ece4", borderRadius: 10, background: "#fafcfa" }}>
        <div style={{ fontSize: 12, color: "#5a7a5a", lineHeight: 1.5, marginBottom: 10 }}>
          Loads daily ~4&nbsp;km gridMET (+ MRMS radar this year) into <code>wny_grid_weather_daily</code> for the 1,271 NY cells that power the Field Mapper per-field weather page.
          Finishes the current year, then loads the chosen number of prior full years. gridMET goes back to 1979, so you can go deeper later.
          Runs one year at a time (~½–1½ min each) — keep this tab open. Idempotent, safe to re-run; more years unlocks year-over-year &amp; vs-normal comparisons.
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", paddingBottom: 12, marginBottom: 12, borderBottom: "1px solid #e4ece4" }}>
          <button onClick={catchUp} disabled={catching || running}
            style={{ padding: "8px 16px", borderRadius: 6, border: "1.5px solid #2d5a10", background: catching || running ? "#f0f4f0" : "#2d5a10", color: catching || running ? "#60735f" : "#fff", fontSize: 12, fontWeight: 800, cursor: catching || running ? "default" : "pointer" }}>
            {catching ? "Syncing…" : "Catch up now"}
          </button>
          <span style={{ fontSize: 12, color: "#5a7a5a", lineHeight: 1.4, flex: 1, minWidth: 200 }}>
            Pulls yesterday's observations early instead of waiting for the 9:00&nbsp;AM&nbsp;ET run — use it after a big rain. Takes ~30–60&nbsp;s.
          </span>
          {catchMsg && (
            <div style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", fontWeight: 700, color: catchMsg.ok ? "#2d6a1a" : "#9a3030", flexBasis: "100%" }}>
              {catchMsg.text}
            </div>
          )}
        </div>
        {!running && !confirming && !done && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ fontSize: 12, color: "#5a7a5a", fontWeight: 700 }}>Prior years to load:</label>
            <select value={depth} onChange={e => setDepth(parseInt(e.target.value, 10))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #cdd9cd", fontSize: 13, color: "#1a2e1a", background: "#fff" }}>
              {[1, 3, 5, 10].map(n => <option key={n} value={n}>{n} year{n > 1 ? "s" : ""}{n === 5 ? " (recommended)" : ""}</option>)}
            </select>
            <button onClick={() => setConfirming(true)}
              style={{ padding: "8px 16px", borderRadius: 6, border: "1.5px solid #2d5a10", background: "#fff", color: "#2d5a10", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
              Run weather backfill
            </button>
          </div>
        )}
        {confirming && !running && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#7a4a00", fontWeight: 700 }}>
              This loads {depth + 1} year{depth ? "s" : ""} ({new Date().getFullYear() - depth}–{new Date().getFullYear()}) — a few minutes total. Continue?
            </span>
            <button onClick={() => { setConfirming(false); run(); }}
              style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#2d5a10", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Yes, run it</button>
            <button onClick={() => setConfirming(false)}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", color: "#5a5a5a", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          </div>
        )}
        {(running || log.length > 0) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: running || done ? 10 : 0 }}>
            {log.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                {r.status === "running"
                  ? <div style={{ width: 12, height: 12, border: "2px solid #d0954a", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  : <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot[r.status], display: "inline-block" }} />}
                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#1a2e1a", minWidth: 42 }}>{r.year}</span>
                <span style={{ color: r.status === "error" ? "#9a3030" : "#5a7a5a", fontVariantNumeric: "tabular-nums" }}>
                  {r.status === "running" ? "loading…" : r.status === "done" ? `✓ ${Number(r.rows || 0).toLocaleString()} rows` : `✗ ${r.msg}`}
                </span>
              </div>
            ))}
            <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
          </div>
        )}
        {done && (
          <button onClick={() => { setDone(false); setLog([]); }}
            style={{ alignSelf: "flex-start", marginTop: 10, padding: "4px 10px", borderRadius: 5, border: "1px solid #ddd", background: "#fff", color: "#5a5a5a", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

// 1 km MRMS rainfall backfill — chunks 2025-01-01 → yesterday into ~40-day ranges
// (each stays under the 300 s function limit) and calls ?mode=mrms1km. Populates the
// native ~1 km per-field rainfall (field_precip_daily) + the model cells.
function MrmsBackfillAdmin({ isMobile }) {
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [log, setLog] = useState([]);
  const [done, setDone] = useState(false);

  async function run() {
    setRunning(true); setDone(false); setLog([]);
    const token = localStorage.getItem("cloverag_access_token");
    if (!token) { setLog([{ label: "—", status: "error", msg: "Not signed in — log out and back in, then retry." }]); setRunning(false); setDone(true); return; }
    const headers = { Authorization: `Bearer ${token}` };
    const start = new Date("2025-01-01T00:00:00Z"), end = new Date(); end.setUTCDate(end.getUTCDate() - 1);
    const jobs = [];
    for (let d = new Date(start); d <= end;) {
      const to = new Date(d); to.setUTCDate(to.getUTCDate() + 13); if (to > end) to.setTime(end.getTime());  // ~14-day chunks: now sampling ~18k fields (not 5k), keep each under the 300s function limit
      jobs.push({ from: d.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) });
      d = new Date(to); d.setUTCDate(d.getUTCDate() + 1);
    }
    const results = [];
    for (const j of jobs) {
      const label = `${j.from} → ${j.to}`;
      results.push({ label, status: "running" }); setLog([...results]);
      try {
        const res = await fetch(`/api/wny-weather-sync?mode=mrms1km&from=${j.from}&to=${j.to}`, { method: "POST", headers });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        results[results.length - 1] = { label, status: "done", rows: data.field_rows || 0, days: data.days_ok || 0 };
      } catch (e) {
        results[results.length - 1] = { label, status: "error", msg: String(e.message || e).slice(0, 160) };
      }
      setLog([...results]);
    }
    setRunning(false); setDone(true);
  }

  const dot = { running: "#d0954a", done: "#2d6a1a", error: "#9a3030" };
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", letterSpacing: "0.02em", marginBottom: 10 }}>🌧️ Per-Field Rainfall Backfill (native ~1&nbsp;km MRMS)</div>
      <div style={{ padding: "14px 16px", border: "1px solid #e4ece4", borderRadius: 10, background: "#fafcfa" }}>
        <div style={{ fontSize: 12, color: "#5a7a5a", lineHeight: 1.5, marginBottom: 10 }}>
          Loads native ~1&nbsp;km gauge-corrected MRMS rainfall for <b>every field</b> (into <code>field_precip_daily</code>) and the model cells, from <b>Jan 1 2025 → yesterday</b>. Runs in ~40-day chunks (~½–1&nbsp;min each) — keep this tab open. Idempotent, safe to re-run. The daily cron keeps it current going forward.
        </div>
        {!running && !confirming && !done && (
          <button onClick={() => setConfirming(true)} style={{ padding: "8px 16px", borderRadius: 6, border: "1.5px solid #1f5fb0", background: "#fff", color: "#1f5fb0", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Run 1&nbsp;km rainfall backfill</button>
        )}
        {confirming && !running && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#1a4a7a", fontWeight: 700 }}>~18&nbsp;months of daily per-field rainfall (Jan 2025→now) — several minutes. Continue?</span>
            <button onClick={() => { setConfirming(false); run(); }} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#1f5fb0", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Yes, run it</button>
            <button onClick={() => setConfirming(false)} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", color: "#5a5a5a", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          </div>
        )}
        {(running || log.length > 0) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: running || done ? 10 : 0 }}>
            {log.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                {r.status === "running"
                  ? <div style={{ width: 12, height: 12, border: "2px solid #d0954a", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  : <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot[r.status], display: "inline-block" }} />}
                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#1a2e1a", minWidth: 168 }}>{r.label}</span>
                <span style={{ color: r.status === "error" ? "#9a3030" : "#5a7a5a", fontVariantNumeric: "tabular-nums" }}>
                  {r.status === "running" ? "loading…" : r.status === "done" ? `✓ ${Number(r.rows || 0).toLocaleString()} field-days (${r.days}d)` : `✗ ${r.msg}`}
                </span>
              </div>
            ))}
            <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
          </div>
        )}
        {done && (
          <button onClick={() => { setDone(false); setLog([]); }} style={{ alignSelf: "flex-start", marginTop: 10, padding: "4px 10px", borderRadius: 5, border: "1px solid #ddd", background: "#fff", color: "#5a5a5a", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Dismiss</button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FIELD MAPPER — Phase A (data tree)
// Per-field inventory sourced from the M13/NAU P27 records (CLU/FSN/tract/
// field/county/crop/acres + R&H lat-lon), persisted to `policy_fields` by the
// daily syncs. Standalone tool — deliberately NOT wired into Market Intelligence.
// Phase B (polygon map from CLU shapefiles) layers on top of this later.
// ─────────────────────────────────────────────────────────────
// Shapefiles wind outer rings CLOCKWISE; GeoJSON / d3.geoPath (spherical) want
// COUNTERCLOCKWISE outer rings — a clockwise ring is interpreted as its inverse
// and fills the whole map. Rewind every outer ring to CCW so polygons render as
// the actual parcel, not its complement.
function ringIsCCW(ring) {
  let a = 0;
  for (let i = 0; i < ring.length - 1; i++) a += ring[i][0] * ring[i+1][1] - ring[i+1][0] * ring[i][1];
  return a >= 0;
}
function rewindGeom(geom) {
  if (!geom || geom.type !== "MultiPolygon") return geom;
  return { type: "MultiPolygon", coordinates: geom.coordinates.map(poly => poly.map((ring, ri) => {
    const wantCCW = ri === 0;                       // outer ring CCW, holes CW
    return ringIsCCW(ring) === wantCCW ? ring : ring.slice().reverse();
  })) };
}

// Leaflet loader (runtime CDN, like d3Promise / SheetJS). Resolves window.L.
let _leafletPromise = null;
// ── Radar frame addressing, shared by every map that draws radar ────────────
// Field Mapper overlays radar on the field map; the Weather page draws it over the
// saved locations. These two functions are what would drift first if each kept
// its own copy — the observed source is a WMS that must be asked in the same
// projection the forecast frames are rendered in, and the forecast source is our
// endpoint with an ensemble floor. Get either subtly wrong and the map still
// draws something plausible, which is the failure mode worth engineering out.

// Snapped to 0.25 deg so distinct viewports collapse onto a small set of URLs
// and panning re-uses cached frames instead of re-rendering ~37 images.
function radarSnapBbox(map) {
  const q = 0.25, b = map.getBounds();
  const w = Math.floor(b.getWest() / q) * q, s = Math.floor(b.getSouth() / q) * q;
  const e = Math.ceil(b.getEast() / q) * q, n = Math.ceil(b.getNorth() / q) * q;
  // Clamp to the HRRR CONUS footprint; the endpoint rejects anything larger.
  return [Math.max(w, -134), Math.max(s, 21), Math.min(e, -61), Math.min(n, 50)];
}

const IEM_N0Q_WMS = "https://mesonet.agron.iastate.edu/cgi-bin/wms/nexrad/n0q.cgi";

function radarFrameUrl(fr, bbox, w, h, mode) {
  if (!fr || !bbox) return null;
  if (fr.kind === "observed") {
    // IEM in EPSG:3857 so the observed frames share the forecast's projection.
    const R = 6378137, D = Math.PI / 180;
    const X = (lon) => R * lon * D;
    const Y = (lat) => R * Math.log(Math.tan(Math.PI / 4 + (lat * D) / 2));
    const mb = [X(bbox[0]), Y(bbox[1]), X(bbox[2]), Y(bbox[3])].map((v) => v.toFixed(2)).join(",");
    return `${IEM_N0Q_WMS}?service=WMS&version=1.1.1`
      + "&request=GetMap&layers=nexrad-n0q&styles=&srs=EPSG:3857&format=image/png&transparent=true"
      + `&width=${w}&height=${h}&bbox=${mb}&TIME=${encodeURIComponent(fr.t)}`;
  }
  // Ensemble only past the backend's minimum lead; nearer than that the lagged
  // members are themselves stale forecasts and outvote the freshest run. The
  // backend enforces the same floor, so this is a URL-cleanliness choice.
  const wantProb = mode === "prob" && fr.leadMin >= 120;
  return `/api/market?fn=radar&op=frame&run=${fr.run}&lead=${fr.leadMin}`
    + `&bbox=${bbox.join(",")}&w=${w}&h=${h}${wantProb ? "&mode=prob" : ""}`;
}

// ── Drawing one frame on a map ───────────────────────────────────────────────
// The two sources want two different Leaflet layers, and that is not an
// implementation detail — it is why the observed radar used to look "crunched".
//
// OBSERVED went out as ONE stretched image: a single GetMap at a fixed 1000x620
// for a bbox snapped to 0.25 deg, which Leaflet then stretched over the map. Two
// things went wrong at once. The requested pixel aspect had nothing to do with the
// bbox's aspect, so the image was squeezed on one axis before being stretched back
// on the other; and the whole thing carried `image-rendering: pixelated`, a rule
// that is honest for a 3 km forecast field and simply destroys a 1 km observation.
// Zoomed to a farm the result was a mosaic of soft rectangles.
//
// A WMS TILE layer fixes it by construction: Leaflet asks for 256px tiles on the
// standard web-Mercator lattice, so every tile is rendered at its own correct
// resolution, pixels are square, nothing is stretched, panning re-uses tiles the
// browser already has, and zooming asks IEM for more detail instead of magnifying
// what it already sent. It is also what makes the layer feel like a radar app.
//
// FORECAST stays an imageOverlay — those are OUR renders, deliberately capped at
// ~1.5 px per HRRR cell and deliberately pixelated, so that zooming past 3 km
// shows cells rather than invented structure.
function ensureRadarPane(map) {
  if (!map.getPane("radarPane")) {
    const pane = map.createPane("radarPane");
    pane.style.zIndex = 350;            // tiles 200 < radar 350 < vectors 400
    pane.style.pointerEvents = "none";  // never steal a click from a field
  }
  if (!document.getElementById("radar-crisp-style")) {
    const st = document.createElement("style");
    st.id = "radar-crisp-style";
    // Scoped to the forecast overlay ONLY. Applied to the observed tiles it turns
    // a 1 km measurement into visible blocks — the exact "too crunched" complaint.
    st.textContent = ".silas-radar-frame{image-rendering:pixelated;image-rendering:crisp-edges;}";
    document.head.appendChild(st);
  }
}

// `refs.current` holds { wms, img }. Only one of the two is on the map at a time,
// so scrubbing across "now" swaps layers exactly once rather than per frame.
function drawRadarFrame(L, map, refs, fr, bbox, opacity, mode) {
  if (!L || !map || !fr) return;
  ensureRadarPane(map);
  const st = refs.current || (refs.current = {});
  const drop = (k) => { if (st[k]) { try { map.removeLayer(st[k]); } catch (e) {} st[k] = null; } };

  if (fr.kind === "observed") {
    drop("img");
    if (!st.wms) {
      st.wms = L.tileLayer.wms(IEM_N0Q_WMS, {
        layers: "nexrad-n0q", format: "image/png", transparent: true,
        pane: "radarPane", opacity, crossOrigin: true, TIME: fr.t,
        // Keep the last good frame painted while the next one loads, so scrubbing
        // and playback never flash the bare basemap between frames.
        keepBuffer: 4, updateWhenIdle: false, className: "silas-radar-wms",
      }).addTo(map);
      st.wmsTime = fr.t;
    } else {
      st.wms.setOpacity(opacity);
      if (st.wmsTime !== fr.t) { st.wms.setParams({ TIME: fr.t }); st.wmsTime = fr.t; }
    }
    return;
  }

  drop("wms");
  st.wmsTime = null;
  const url = radarFrameUrl(fr, bbox, 1000, 620, mode);
  if (!url || !bbox) return;
  const bounds = [[bbox[1], bbox[0]], [bbox[3], bbox[2]]];
  if (!st.img) {
    st.img = L.imageOverlay(url, bounds, {
      opacity, pane: "radarPane", interactive: false, crossOrigin: true,
      className: "silas-radar-frame",
    }).addTo(map);
  } else {
    try {
      st.img.setBounds(L.latLngBounds(bounds));
      st.img.setUrl(url);
      st.img.setOpacity(opacity);
    } catch (e) {}
  }
}

function clearRadarFrame(map, refs) {
  const st = refs.current;
  if (!map || !st) return;
  for (const k of ["wms", "img"]) if (st[k]) { try { map.removeLayer(st[k]); } catch (e) {} st[k] = null; }
  st.wmsTime = null;
}

function loadLeaflet() {
  if (typeof window !== "undefined" && window.L) return Promise.resolve(window.L);
  if (_leafletPromise) return _leafletPromise;
  _leafletPromise = new Promise((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet"; css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
    const s = document.createElement("script");
    s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.onload = () => resolve(window.L); s.onerror = reject;
    document.head.appendChild(s);
  });
  return _leafletPromise;
}

// shpjs loader (runtime CDN, like loadLeaflet / SheetJS). Resolves window.shp —
// shp(zipArrayBuffer) → GeoJSON FeatureCollection (or array of them, one per
// layer), reprojected to WGS84 via the .prj. Used by the boundary importer.
let _shpPromise = null;
function loadShp() {
  if (typeof window !== "undefined" && window.shp) return Promise.resolve(window.shp);
  if (_shpPromise) return _shpPromise;
  _shpPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://unpkg.com/shpjs@4.0.4/dist/shp.min.js";
    s.onload = () => resolve(window.shp); s.onerror = reject;
    document.head.appendChild(s);
  });
  return _shpPromise;
}

// Geodesic centroid + acres for a GeoJSON Polygon / MultiPolygon. acres is the
// spherical ring area (outer − holes), good to <0.5% at field scale; centroid is
// the area-weighted polygon centroid (so it sits inside the field for map zoom /
// county point-in-polygon). Mirrors what the shapefile bundle stored per CLU.
// Extracted to src/geoArea.js so the PRF Advisor's grid-exposure calculation
// measures acres the SAME way the Field Mapper does. Two acreage formulas in
// one repo is two answers to "how big is this field", and the PRF answer ends
// up on an application. The body moved unchanged — same radius, same spherical
// excess, same divisor — so no acre in the book moved with it.
function _geomCentroidAcres(geom) {
  return _sharedCentroidAcres(geom);
}

// County boundaries from the Census TIGERweb service — authoritative, full
// geometry simplified to ~100 m (maxAllowableOffset) so the overlay stays light
// (~75 KB/state) without the gross generalization of cartographic-boundary files.
// Fetched per state, cached for the session. CORS is fine — ArcGIS echoes Origin.
const _countyGeoByState = {};
function loadCountyGeoForState(stateFips) {
  if (_countyGeoByState[stateFips]) return _countyGeoByState[stateFips];
  const url = "https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/State_County/MapServer/1/query"
    + `?where=STATE%3D'${stateFips}'&outFields=NAME&returnGeometry=true&f=geojson&maxAllowableOffset=0.001`;
  _countyGeoByState[stateFips] = fetch(url).then(r => r.ok ? r.json() : null).catch(() => null);
  return _countyGeoByState[stateFips];
}

// Point-in-polygon (ray casting) for GeoJSON Polygon/MultiPolygon — used to
// resolve a field's PHYSICAL county from its boundary centroid, since the P27
// county FIPS is the farm's administrative county and border-straddling farms
// record fields under the home county even when they sit across the line.
function _ringContains(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
    if (((yi > pt[1]) !== (yj > pt[1])) && (pt[0] < (xj - xi) * (pt[1] - yi) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}
function _polyContains(pt, geom) {
  const polys = geom.type === "MultiPolygon" ? geom.coordinates : geom.type === "Polygon" ? [geom.coordinates] : [];
  for (const rings of polys) {
    if (!rings.length || !_ringContains(pt, rings[0])) continue;
    let inHole = false;
    for (let h = 1; h < rings.length; h++) if (_ringContains(pt, rings[h])) { inHole = true; break; }
    if (!inHole) return true;
  }
  return false;
}
// Bounding box of any GeoJSON geometry, as [minLon, minLat, maxLon, maxLat].
//
// The walker recurses on nesting depth, which is only safe if it stops at a
// coordinate PAIR. It used to test `typeof c[0] === "number"` — true for a ring's
// [lon,lat] pair, but a POINT's coordinates are two bare numbers, and indexing a
// number yields undefined, so it recursed into the number itself and called
// .forEach on it. That threw "e.forEach is not a function" and took down the whole
// Field Mapper. Guard on "is this an array" instead, which is the actual question.
function _geomBbox(geom) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const walk = (a) => {
    if (!Array.isArray(a)) return;
    if (typeof a[0] === "number" && typeof a[1] === "number") {   // a coordinate pair
      if (a[0] < minX) minX = a[0]; if (a[0] > maxX) maxX = a[0];
      if (a[1] < minY) minY = a[1]; if (a[1] > maxY) maxY = a[1];
      return;
    }
    for (const c of a) walk(c);
  };
  walk(geom && geom.coordinates);
  return [minX, minY, maxX, maxY];
}

// ── GROUPING FIELDS ONTO PRINTED PAGES ──────────────────────────────────────
// One map per field wastes a ream on a book that averages 32 fields per grower;
// one map for the whole farm is unreadable at a 9-mile spread (45 miles on the
// worst). So fields are grouped by PROXIMITY and each group becomes one page.
//
// THE CONSTRAINT IS "DOES IT FIT ON THE PAGE", NOT "ARE THEY WITHIN X MILES".
// A distance threshold (DBSCAN-style) can chain a long straggling line of fields
// into one cluster whose bounding box is enormous — every pair is close, the group
// is not. So this merges only while the MERGED BOUNDING BOX still fits the page's
// ground span, which is exactly the thing that decides legibility. Nothing else
// needs tuning, and there is no k to pick.
//
// Single-linkage agglomerative: repeatedly merge the closest legal pair until no
// legal merge remains. O(n^2 log n) at n=32 typical / few hundred worst — trivial.
const MI_PER_DEG_LAT = 69.0;
const milesPerDegLon = (lat) => MI_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);

// Page aspect: letter landscape minus margins, map occupying the upper block.
const PRINT_PAGE_ASPECT = 10.1 / 6.4;   // w/h of the map box in inches

// Does a bbox fit inside a page whose LONGER ground dimension is `spanMi`?
// The page is wider than tall, so the width budget is the full span and the
// height budget is span/aspect — a tall thin cluster is NOT the same as a wide one.
function _bboxFitsPage(b, spanMi) {
  const midLat = (b.minLat + b.maxLat) / 2;
  const wMi = (b.maxLon - b.minLon) * milesPerDegLon(midLat);
  const hMi = (b.maxLat - b.minLat) * MI_PER_DEG_LAT;
  return wMi <= spanMi && hMi <= spanMi / PRINT_PAGE_ASPECT;
}
const _bboxUnion = (a, b) => ({
  minLat: Math.min(a.minLat, b.minLat), maxLat: Math.max(a.maxLat, b.maxLat),
  minLon: Math.min(a.minLon, b.minLon), maxLon: Math.max(a.maxLon, b.maxLon),
});
const _bboxCenter = (b) => [(b.minLat + b.maxLat) / 2, (b.minLon + b.maxLon) / 2];

// items: [{ key, lat, lon, ... }] — one per field (its centroid).
// Returns [{ items[], bbox, centerLat, centerLon }], ordered north-west → south-east
// so the printed stack walks the farm in a sane order rather than hash order.
//
// THIS IS O(n) AND HAS TO BE. The first version was agglomerative — repeatedly merge
// the closest legal pair — which rescans every pair on every merge: O(n^3). That is
// fine at the 32 fields a single farm holds, and it is what I tested. The Field
// Mapper's DEFAULT view is the whole book: 14,649 fields. It hung the tab hard
// enough for Chrome to offer to kill the page.
//
// So the grid does the work. Cell dimensions ARE the page's ground span, anchored on
// the data's own south-west corner, so every bucket's bbox fits a page by
// construction — the fit constraint is satisfied without ever testing a pair. Then
// ONE neighbour pass merges adjacent buckets where the union still fits, which
// recovers most of what the greedy version gave: without it, a tight cluster
// straddling a cell boundary prints as two half-empty pages.
//
// Tradeoff, stated plainly: this is not the optimal packing the O(n^3) version
// approached. It can still split a group across a boundary two neighbour-hops apart.
// It is deterministic, it always fits the page, and it returns instantly on the
// whole book — which the optimal one did not do at all.
function groupFieldsForPrint(items, spanMi) {
  const pts = (items || []).filter(it => Number.isFinite(it.lat) && Number.isFinite(it.lon));
  if (!pts.length) return [];

  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity;
  for (const p of pts) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
  }
  const cellH = (spanMi / PRINT_PAGE_ASPECT) / MI_PER_DEG_LAT;      // degrees of latitude
  // Cell WIDTH is sized at the latitude CLOSEST TO THE EQUATOR in the data, not the
  // mean. A degree of longitude is widest there, so a cell sized on the mean runs
  // over budget everywhere south of it — ~1.6% across a 2-degree book, which put
  // real pages past the page-fit constraint this whole design rests on. Sizing on
  // the widest latitude makes every cell fit at every latitude in view.
  const widestLat = Math.min(Math.abs(minLat), Math.abs(maxLat));
  const cellW = spanMi / milesPerDegLon(widestLat);                 // degrees of longitude
  if (!(cellH > 0) || !(cellW > 0)) return [];

  const buckets = new Map();
  for (const it of pts) {
    const r = Math.floor((it.lat - minLat) / cellH), c = Math.floor((it.lon - minLon) / cellW);
    const k = r + "|" + c;
    let b = buckets.get(k);
    if (!b) { b = { r, c, items: [], bbox: { minLat: it.lat, maxLat: it.lat, minLon: it.lon, maxLon: it.lon } };
              buckets.set(k, b); }
    b.items.push(it);
    if (it.lat < b.bbox.minLat) b.bbox.minLat = it.lat;
    if (it.lat > b.bbox.maxLat) b.bbox.maxLat = it.lat;
    if (it.lon < b.bbox.minLon) b.bbox.minLon = it.lon;
    if (it.lon > b.bbox.maxLon) b.bbox.maxLon = it.lon;
  }

  // One neighbour pass, union-find style. Only forward neighbours are considered so
  // each unordered pair is visited once and the result cannot depend on iteration
  // order flipping a merge back and forth.
  const parent = new Map();
  const find = (k) => { while (parent.get(k) !== k) { parent.set(k, parent.get(parent.get(k))); k = parent.get(k); } return k; };
  const box = new Map();
  for (const [k, b] of buckets) { parent.set(k, k); box.set(k, b.bbox); }
  for (const [k, b] of buckets) {
    for (const [dr, dc] of [[0, 1], [1, 0], [1, 1], [1, -1]]) {
      const nk = (b.r + dr) + "|" + (b.c + dc);
      if (!buckets.has(nk)) continue;
      const a = find(k), z = find(nk);
      if (a === z) continue;
      const merged = _bboxUnion(box.get(a), box.get(z));
      if (!_bboxFitsPage(merged, spanMi)) continue;
      parent.set(z, a); box.set(a, merged);
    }
  }

  const out = new Map();
  for (const [k, b] of buckets) {
    const root = find(k);
    let g = out.get(root);
    if (!g) { g = { items: [], bbox: box.get(root) }; out.set(root, g); }
    for (const it of b.items) g.items.push(it);
  }
  return [...out.values()]
    .map(c => { const [lat, lon] = _bboxCenter(c.bbox); return { ...c, centerLat: lat, centerLon: lon }; })
    .sort((a, b) => (b.centerLat - a.centerLat) || (a.centerLon - b.centerLon));
}

// Build the print document for grouped field maps. One <section> per page, each
// with its own Leaflet map over Esri imagery plus a table of the fields on it.
//
// THE WHOLE TRICK IS NOT PRINTING TOO EARLY. window.print() fires synchronously
// against whatever is painted; a Leaflet map that hasn't finished pulling tiles
// prints as grey boxes, which is the classic way this feature ships broken. So
// the child document gates print() on every tile layer's 'load' event, with a
// hard timeout so a slow or failing tile server still yields a printable page
// (boundaries and labels render regardless — imagery is the part that can be late).
function buildFieldMapsPrintHtml(pages, meta) {
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const acres = (n) => n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
  const today = new Date().toLocaleDateString();

  // Per page: the payload the child script needs, and the printed field table.
  const payload = pages.map(pg => ({
    bbox: pg.bbox,
    feats: pg.fields.map((f, i) => {
      const p = f.properties;
      return { n: i + 1, geometry: f.geometry, color: fieldCropColor(p.crop, p.type_code), pp: !!p.ppFull };
    }),
  }));

  // ⚠ EVERY SHEET NAMES WHOSE FIELDS ARE ON IT (BUG-067, 2026-09-11). The header
  // used to print the caller's title, which is the literal "All fields" whenever no
  // farm is ticked in the mapper — the default, and always the case for a portal
  // user. So a farm manager printing eight farms got a stack of sheets reading
  // "All fields · Map 1 of 28" with no farm and no policy anywhere on them, and
  // could not tell them apart once they left the printer. The identity is read
  // off the FIELDS ON THE SHEET, never off a selection, so a sheet can only ever
  // name the farms whose ground it actually shows.
  const uniq = (xs) => [...new Set(xs.filter(Boolean).map(String))].sort();
  // The county carries its OWN state, never the page's — one sheet can hold ground
  // in two states (Bridgewater Farming: STEUBEN Co., IN beside WILLIAMS Co., OH),
  // so a single state stamped on the header would be wrong on half the rows. A
  // field whose state cannot be established prints the bare county rather than a
  // borrowed one.
  const countyLabel = (p) => p.county ? `${p.county} Co.${p.state ? `, ${p.state}` : ""}` : null;
  const whoOn = (fields) => ({
    farms: uniq(fields.map((f) => f.properties.farmer)),
    policies: uniq(fields.map((f) => f.properties.policy_number)),
    counties: uniq(fields.map((f) => countyLabel(f.properties))),
  });
  const allFarms = uniq(pages.flatMap((pg) => pg.fields.map((f) => f.properties.farmer)));
  // The document title is also the default file name when it is saved as a PDF.
  const docTitle = allFarms.length ? `${allFarms.join(" · ")} — field maps` : (meta.title || "Field maps");
  const sections = pages.map((pg, pi) => {
    const who = whoOn(pg.fields);
    const rows = pg.fields.map((f, i) => {
      const p = f.properties;
      const nm = (meta.labels || {})[String(p.clu_id || "").toLowerCase()] || "";
      const ftf = [p.fsn, p.tract, p.field].filter(Boolean).join("-") || "—";
      const cornT = cornTypeLabel(p);
      return `<tr>
        <td class="n"><span class="pin" style="background:${esc(fieldCropColor(p.crop, p.type_code))}">${i + 1}</span></td>
        <td>${esc(nm || ftf)}${nm ? ` <span class="dim">${esc(ftf)}</span>` : ""}${who.farms.length > 1 && p.farmer ? ` <span class="dim">· ${esc(p.farmer)}</span>` : ""}</td>
        <td>${esc(p.crop || "—")}${cornT && cornT !== "Grain (GSG)" ? ` <span class="dim">${esc(cornT)}</span>` : ""}</td>
        <td class="r">${acres(p.acres)}</td>
        <td>${p.ppFull ? "Prevent plant" : p.planted ? esc(p.planted) : "—"}</td>
      </tr>`;
    }).join("");
    const totAc = pg.fields.reduce((s, f) => s + (Number(f.properties.acres) || 0), 0);
    return `<section class="page">
      <header>
        <div><h1>${esc(who.farms.length ? who.farms.join(" · ") : (meta.title || "Field maps"))}</h1>
             ${(() => {
               const sub = [
                 who.policies.length ? `Policy ${who.policies.join(", ")}` : null,
                 who.counties.length ? who.counties.join(", ") : (meta.subtitle || null),
               ].filter(Boolean).join(" · ");
               return sub ? `<div class="sub">${esc(sub)}</div>` : "";
             })()}</div>
        <div class="meta">Map ${pi + 1} of ${pages.length}<br><span class="dim">${esc(today)}</span></div>
      </header>
      <div class="map" id="map${pi}"></div>
      <table>
        <thead><tr><th class="n">#</th><th>Field</th><th>Crop</th><th class="r">Acres</th><th>Planted</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td></td><td>${pg.fields.length} field${pg.fields.length === 1 ? "" : "s"}</td><td></td>
          <td class="r">${acres(totAc)}</td><td></td></tr></tfoot>
      </table>
    </section>`;
  }).join("");

  return `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(docTitle)}</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css">
<style>
  @page { size: letter landscape; margin: 0.4in; }
  * { box-sizing: border-box; }
  body { margin:0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
         color:#1f3320; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { page-break-after: always; break-after: page; padding: 0 0 6px; }
  .page:last-child { page-break-after: auto; break-after: auto; }
  header { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #2d4a2d;
           padding-bottom:5px; margin-bottom:7px; }
  h1 { font-size:15pt; margin:0; }
  .sub { font-size:9pt; color:#5a6b5a; margin-top:2px; }
  .meta { font-size:9pt; text-align:right; line-height:1.35; }
  .dim { color:#8a978a; font-weight:400; }
  .map { width:100%; height:4.35in; border:1px solid #cdd9cd; border-radius:3px; background:#e8ece8; }
  table { width:100%; border-collapse:collapse; font-size:8.5pt; margin-top:7px; }
  th, td { text-align:left; padding:2.5px 5px; border-bottom:1px solid #e3ebe3; }
  thead th { background:#f2f6f2; font-size:7.5pt; text-transform:uppercase; letter-spacing:.04em; color:#5a6b5a; }
  tfoot td { font-weight:700; border-top:1.5px solid #2d4a2d; border-bottom:none; }
  td.r, th.r { text-align:right; }
  td.n, th.n { width:22px; }
  .pin { display:inline-block; width:16px; height:16px; line-height:16px; border-radius:50%; text-align:center;
         font-size:8pt; font-weight:800; color:#1f3320; border:1px solid rgba(0,0,0,.35); }
  .lbl { font: 800 11px/1 sans-serif; color:#fff; text-shadow:0 0 3px #000, 0 0 3px #000;
         text-align:center; white-space:nowrap; }
  .status { position:fixed; left:0; right:0; top:45%; text-align:center; font-size:11pt; color:#5a6b5a; }
  @media print { .status { display:none; } }
</style></head><body>
<div class="status" id="status">Loading imagery…</div>
${sections}
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<script>
(function () {
  var PAGES = ${JSON.stringify(payload)};
  var TILE = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
  var done = false;
  function go() {
    if (done) return; done = true;
    var s = document.getElementById("status"); if (s) s.style.display = "none";
    setTimeout(function () { window.print(); }, 250);
  }
  if (!window.L) { go(); return; }   // Leaflet blocked — still print the tables

  var waits = PAGES.map(function (pg, i) {
    return new Promise(function (resolve) {
      var el = document.getElementById("map" + i);
      if (!el) return resolve();
      var map = L.map(el, { zoomControl: false, attributionControl: false,
                            dragging: false, scrollWheelZoom: false, keyboard: false });
      var tiles = L.tileLayer(TILE, { maxZoom: 19 }).addTo(map);
      // Boundaries first, labels on top.
      PAGES[i].feats.forEach(function (ft) {
        L.geoJSON({ type: "Feature", geometry: ft.geometry }, { style: {
          color: "#ffffff", weight: 1.6, opacity: 0.95,
          fillColor: ft.color, fillOpacity: ft.pp ? 0.35 : 0.55,
          dashArray: ft.pp ? "5,3" : null } }).addTo(map);
      });
      var b = pg.bbox;
      map.fitBounds([[b.minLat, b.minLon], [b.maxLat, b.maxLon]], { padding: [16, 16], maxZoom: 17 });
      PAGES[i].feats.forEach(function (ft) {
        try {
          var c = L.geoJSON({ type: "Feature", geometry: ft.geometry }).getBounds().getCenter();
          L.marker(c, { interactive: false, icon: L.divIcon({
            className: "", html: '<div class="lbl">' + ft.n + '</div>',
            iconSize: [22, 12], iconAnchor: [11, 6] }) }).addTo(map);
        } catch (e) {}
      });
      map.invalidateSize();
      // Resolve on the layer reporting every visible tile in, or on a per-map
      // timeout — a stalled tile must not hold the whole document hostage.
      var settled = false;
      var finish = function () { if (!settled) { settled = true; resolve(); } };
      tiles.on("load", finish);
      setTimeout(finish, 12000);
    });
  });
  Promise.all(waits).then(function () { setTimeout(go, 400); });
  setTimeout(go, 20000);            // absolute backstop
})();
</script>
</body></html>`;
}

// FSA PLANTING REPORT — as-planted evidence for acreage reporting.
//
// Three rules this document must not break, all of them because it leaves the
// building and goes to a federal agency on the grower's behalf:
//
//   ACREAGE IS THE POLICY'S, NOT THE MACHINE'S. Machine acres are where the planter
//   drove. On CY2026 they total 87.8% of reported and agree within 2% on only 14% of
//   fields, erring BOTH ways (Sullivan's run 18% over). They print as a cross-check
//   column, never as the reportable figure.
//
//   EVERY FIELD IS LISTED. No farm has full machine coverage, so a report showing
//   only the evidenced fields reads as complete and is not.
//
//   PREVENT PLANT IS NOT MISSING DATA. PP acres are reported and were never planted;
//   they get their own status rather than appearing as a gap.
// Every row in this report IS a planting pass, so there is no status to encode —
// pins take the crop's own colour, which is what the reader already knows from the
// Field Mapper.
const fsaPinColor = (r) => fieldCropColor(r.crop, /SILAGE/.test(String(r.crop || "")) ? "026" : "016");
function buildFsaPlantingPrintHtml(data, meta) {
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const ac = (n) => n == null ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const rows = data.rows || [], s = data.summary || {};
  const today = new Date().toLocaleDateString();

  // Pages of maps, grouped by proximity — same rule as the field-map print.
  const withGeom = rows.filter(r => meta.geoms[String(r.clu_id || "").toLowerCase()]);
  const items = withGeom.map(r => {
    const g = meta.geoms[String(r.clu_id || "").toLowerCase()];
    const b = _geomBbox(g);
    return { key: r.clu_id, lat: (b[1] + b[3]) / 2, lon: (b[0] + b[2]) / 2, r, g,
             bbox: { minLat: b[1], maxLat: b[3], minLon: b[0], maxLon: b[2] } };
  });
  const groups = groupFieldsForPrint(items, meta.spanMi || 2);
  const pages = groups.map(gr => ({
    bbox: gr.items.map(i => i.bbox).reduce(_bboxUnion),
    fields: gr.items.map(i => i.r), geoms: gr.items.map(i => i.g),
  }));

  // ── EVERY PAGE MUST BE ATTRIBUTABLE ON ITS OWN ─────────────────────────────
  // This document goes to a federal office and its pages get separated there. A
  // sheet that names only a county is not attributable: BERRIEN exists in Michigan
  // and Georgia, VAN BUREN in Michigan, Iowa, Arkansas and Tennessee.
  //
  // THE STATE COMES FROM `fieldStateOf` — the SAME rule the map print uses (#154),
  // not a second one. Measured on CY2026: all 15,932 keyed rows carry an
  // `insured_county`, so rung 1 (the POLICY's own state) answers every one of them
  // and the `state_fips` fallback is never reached today.
  //
  // ⚠ AND IT IS REFUSED WHERE THE PRINTED COUNTY IS NOT THE INSURED ONE. This
  // report prints the FSA ADMINISTRATIVE county — the right county for an FSA
  // filing — while the state we can vouch for belongs to the INSURED county. On the
  // 1,057 CY2026 rows where those differ, 31 differ ACROSS A STATE LINE, and every
  // one of the 31 is in this shape:
  //
  //   admin BRADFORD (PA)  ·  insured TIOGA (NY)   ·  MP-0936367
  //   admin WILLIAMS (OH)  ·  insured STEUBEN (IN) ·  IN-942-6101305-26
  //
  // Pairing them prints "BRADFORD Co., NY" and "WILLIAMS Co., IN" — counties that
  // do not exist in those states, on a federal acreage document. That is #154's own
  // defect arriving from the other direction, so the state is withheld rather than
  // guessed, and the insured county is named instead.
  const stateOf = (r) => {
    const st = fieldStateOf({
      insured_county: r.insured_county,
      policies: { state: r.policy_state },
      state_fips: r.state_fips,
    });
    if (!st) return null;
    const admin = String(r.county || "").toUpperCase();
    const ins = String(r.insured_county || "").toUpperCase();
    if (!admin || !ins || admin === ins) return st;
    // The counties differ, so `st` belongs to a county this page is not printing.
    // It is still correct whenever the FSA ADMIN county is in the same state — and
    // `state_fips` is exactly that state, which is the one thing it IS an authority
    // for. Resolved through the SAME shipped function (its rung 3), not a second
    // table: refusing on a bare county difference would drop a true state from the
    // 13 CY2026 report rows where the two counties differ INSIDE one state
    // (VAN BUREN admin / BERRIEN insured, both MI), while catching nothing extra.
    const adminSt = fieldStateOf({ state_fips: r.state_fips });
    return adminSt && adminSt === st ? st : null;
  };
  // ⚠ A PAGE SPANNING TWO POLICIES NAMES NEITHER. 108 of this book's CY2026 farms
  // carry more than one policy — RED DAWN itself carries two — so "whichever came
  // first" would be wrong on real pages, not hypothetical ones, and a wrong policy
  // number on an FSA filing is worse than no policy number. A page prints one only
  // when every field on it agrees.
  const policyLine = (fields) => {
    const nums = new Set();
    let ambiguous = false, missing = 0;
    for (const r of fields) {
      if (r.policy_ambiguous) { ambiguous = true; continue; }
      if (r.policy_number) nums.add(r.policy_number); else missing++;
    }
    const one = [...nums][0];
    if (ambiguous) return "More than one policy claims a field on this page — see the table";
    if (nums.size > 1) return `${nums.size} policies on this page — see the table`;
    if (nums.size === 1 && !missing) return `Policy ${one}`;
    if (nums.size === 1) return `Policy ${one} · ${missing} field${missing === 1 ? "" : "s"} not on this year's acreage report`;
    return "No policy on this year's acreage report for these fields";
  };
  // ⚠ COLLAPSE PER COUNTY, OR ONE COUNTY PRINTS TWICE. A county holding both keyed
  // and un-keyed fields yields "VAN BUREN Co., MI" from one row and a bare
  // "VAN BUREN Co." from the next, which reads as two counties. The per-ROW truth
  // still lives in the St column; this line is the page's geographic context.
  // A county resolving to TWO states is NOT merged — that is the cross-state case
  // and both belong on the page.
  const geoLine = (fields) => {
    const byCounty = new Map();
    for (const r of fields) {
      const c = r.county || r.insured_county;
      if (!c) continue;
      if (!byCounty.has(c)) byCounty.set(c, new Set());
      const st = stateOf(r);
      if (st) byCounty.get(c).add(st);
    }
    const out = [];
    for (const [c, sts] of byCounty) {
      if (!sts.size) out.push(`${c} Co.`);
      else for (const st of sts) out.push(`${c} Co., ${st}`);
    }
    return out.join(" · ");
  };
  // One line, used by every page type, so a page cannot acquire identity on one
  // template and lose it on another.
  const idLine = (fields) => esc([policyLine(fields), geoLine(fields)].filter(Boolean).join("  ·  "));

  const tableRows = rows.map(r => {
    const nm = (meta.labels || {})[String(r.clu_id || "").toLowerCase()] || "";
    return `<tr>
      <td><b>${esc(r.ftf || "—")}</b>${nm ? `<div class="dim">${esc(nm)}</div>` : ""}</td>
      <td>${esc(r.county || "—")}</td>
      <td>${esc(stateOf(r) || "—")}</td>
      <td>${esc(jdCropLabel(r.crop))}</td>
      <td class="r"><b>${ac(r.planted_acres)}</b></td>
      <td>${r.plant_date ? esc(r.plant_date) : '<span class="dim">—</span>'}
          ${r.plant_date_last ? `<div class="dim">to ${esc(r.plant_date_last)}</div>` : ""}
          ${r.plant_date_sane === false ? '<div class="warn">outside window</div>' : ""}</td>
      <td class="r dim">${r.seed_rate != null ? r.seed_rate.toLocaleString() : "—"}</td>
      <td class="r dim">${r.policy_acres == null ? "—" : ac(r.policy_acres)}</td>
      <td class="dim">${r.policy_ambiguous
        ? `<span class="warn">${esc((r.policy_numbers || []).join(" / "))}</span>`
        : esc(r.policy_number || "—")}</td>
    </tr>`;
  }).join("");

  // EXCEPTIONS — planted ground this report cannot place on an FSA field. Split by
  // cause, because "you farm new ground" and "our processing is behind" are not the
  // same message and must not be added together.
  const exAll = data.exceptions || [];
  const exUn = exAll.filter(e => e.kind === "unmapped" || e.kind === "partly_unmapped");
  const exNo = exAll.filter(e => e.kind === "no_coverage_yet");
  const exRows = (list) => list.map(e => `<tr>
      <td><b>${esc(e.field_name || "—")}</b></td>
      <td>${esc(jdCropLabel(e.crop))}</td>
      <td>${e.plant_date ? esc(e.plant_date) : "—"}</td>
      <td class="r">${ac(e.machine_acres)}</td>
      <td class="r dim">${ac(e.placed_acres)}</td>
      <td class="r"><b>${ac(e.unmapped_acres)}</b></td>
    </tr>`).join("");

  const flags = [];
  if (s.unmapped_acres > 0.5) flags.push(`<b>${ac(s.unmapped_acres)}</b> planted acres fall outside every mapped FSA field — see the exceptions page.`);
  if (s.no_coverage_acres > 0.5) flags.push(`<b>${ac(s.no_coverage_acres)}</b> planted acres have no coverage map built yet.`);
  if (s.implausible_dates) flags.push(`<b>${s.implausible_dates}</b> plant date(s) fall outside the crop's planting window.`);

  // Each page carries only the machine rasters that actually overlap it — shipping
  // every farm raster to every page is how a 12-page report becomes 60MB.
  const overlaps = (b, r) => !(r.bounds[1][0] < b.minLat || r.bounds[0][0] > b.maxLat ||
                               r.bounds[1][1] < b.minLon || r.bounds[0][1] > b.maxLon);
  const payload = pages.map(pg => ({
    bbox: pg.bbox,
    feats: pg.geoms.map((g, j) => ({ n: j + 1, geometry: g,
      color: fsaPinColor(pg.fields[j]) })),
    rasters: (meta.rasters || []).filter(r => overlaps(pg.bbox, r))
      .map(r => ({ url: r.url, bounds: r.bounds })),
  }));
  // Which crops' rate scales are represented, for the per-page legend.
  const rasterScales = [...new Map((meta.rasters || []).map(r => [r.crop, r])).values()];
  // ⚠ THESE THREE MUST STAY ABOVE mapPages, and that is arithmetic rather than
  // style. `mapPages` is a `.map(...).join("")` — it RUNS IMMEDIATELY, and its
  // template reads `payload[i]` and `rasterScales`, so declaring them below it is a
  // temporal dead zone. That is what shipped on 2026-08-13 and it threw
  // ReferenceError on EVERY FSA planting report from then until 2026-09-12:
  // window.open succeeded, document.open() wiped the "Building planting report…"
  // placeholder, and the grower was left staring at a permanently blank window with
  // nothing on screen to say why. An async handler's throw is an unhandled
  // rejection, so it reached no error boundary and no log the page could show.

  const mapPages = pages.map((pg, i) => `
    <section class="page">
      <header>
        <div><h1>${esc(meta.farmer)}</h1>
             <div class="sub">${esc(meta.year)} planting${meta.crops ? " · " + esc(meta.crops.join(", ")) : ""} — map ${i + 1} of ${pages.length}</div>
             <div class="ident">${idLine(pg.fields)}</div></div>
        <div class="meta">Clover Ag Risk Management<br><span class="dim">${esc(today)}</span></div>
      </header>
      <div class="map" id="m${i}"></div>
      ${payload[i].rasters.length ? `<div class="ramp">
        <span>Machine seeding rate —</span>
        ${rasterScales.map(rs => `<span class="rampitem"><b>${esc(rs.crop)}</b>
          ${rs.lo.toLocaleString()} <span class="bar"></span> ${rs.hi.toLocaleString()}</span>`).join("")}
        <span class="dim">as-planted coverage from the planter's own record</span>
      </div>` : ""}
      <table class="mini">
        <thead><tr><th class="n">#</th><th>Farm-Tract-Field</th><th>County</th><th>St</th><th>Crop</th><th class="r">Planted ac</th><th>Plant date</th><th class="r">Seed rate</th><th>Policy</th></tr></thead>
        <tbody>${pg.fields.map((r, j) => `<tr>
          <td class="n"><span class="pin" style="background:${fsaPinColor(r)}">${j + 1}</span></td>
          <td><b>${esc(r.ftf || "—")}</b></td>
          <td>${esc(r.county || "—")}</td>
          <td>${esc(stateOf(r) || "—")}</td>
          <td>${esc(jdCropLabel(r.crop))}</td>
          <td class="r"><b>${ac(r.planted_acres)}</b></td>
          <td>${r.plant_date ? esc(r.plant_date) : "—"}${r.plant_date_sane === false ? '<div class="warn">outside window</div>' : ""}</td>
          <td class="r dim">${r.seed_rate != null ? r.seed_rate.toLocaleString() : "—"}</td>
          <td class="dim">${r.policy_ambiguous
            ? `<span class="warn">${esc((r.policy_numbers || []).join(" / "))}</span>`
            : esc(r.policy_number || "—")}</td></tr>`).join("")}</tbody>
        <tfoot><tr><td></td><td>${pg.fields.length} field${pg.fields.length === 1 ? "" : "s"}</td><td></td><td></td><td></td>
          <td class="r">${ac(pg.fields.reduce((s, r) => s + (r.planted_acres || 0), 0))}</td>
          <td></td><td></td><td></td></tr></tfoot>
      </table>
    </section>`).join("");


  return `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(meta.farmer)} — ${esc(meta.year)} planting report</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css">
<style>
  @page { size: letter landscape; margin: 0.4in; }
  * { box-sizing: border-box; }
  body { margin:0; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; color:#1f3320;
         -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .page { page-break-after: always; break-after: page; }
  .page:last-child { page-break-after:auto; break-after:auto; }
  header { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #2d4a2d;
           padding-bottom:5px; margin-bottom:8px; }
  h1 { font-size:15pt; margin:0; } h2 { font-size:11pt; margin:14px 0 5px; }
  .sub { font-size:9pt; color:#5a6b5a; margin-top:2px; }
  /* The line that makes a separated page attributable. Same size as the sub so it
     survives a photocopy, darker and heavier so it reads as identity, not caption. */
  .ident { font-size:8.5pt; color:#2d4a2d; margin-top:2px; font-weight:600; }
  .meta { font-size:8.5pt; text-align:right; line-height:1.35; }
  .dim { color:#8a978a; font-weight:400; font-size:7.5pt; }
  .warn { color:#8c3a3a; font-weight:700; font-size:7.5pt; }
  .map { width:100%; height:4.1in; border:1px solid #cdd9cd; border-radius:3px; background:#e8ece8; }
  table { width:100%; border-collapse:collapse; font-size:8.5pt; }
  table.mini { margin-top:7px; font-size:8pt; }
  th,td { text-align:left; padding:2.5px 5px; border-bottom:1px solid #e3ebe3; vertical-align:top; }
  thead th { background:#f2f6f2; font-size:7.5pt; text-transform:uppercase; letter-spacing:.04em; color:#5a6b5a; }
  td.r, th.r { text-align:right; } td.n, th.n { width:22px; }
  .pin { display:inline-block; width:16px; height:16px; line-height:16px; border-radius:50%; text-align:center;
         font-size:8pt; font-weight:800; color:#fff; }
  .chip { display:inline-block; padding:1px 6px; border-radius:9px; font-size:7pt; font-weight:700; border:1px solid; }
  .lbl { font:800 11px/1 sans-serif; color:#fff; text-shadow:0 0 3px #000,0 0 3px #000; text-align:center; }
  .kpis { display:flex; gap:18px; flex-wrap:wrap; margin:8px 0 4px; }
  .kpi b { font-size:14pt; } .kpi div { font-size:8pt; color:#5a6b5a; }
  .attn { border:1.5px solid #c08a2c; background:#fdf7ea; border-radius:4px; padding:9px 12px; margin:10px 0; font-size:9pt; }
  .attn h3 { margin:0 0 5px; font-size:9.5pt; }
  .attn li { margin:2px 0; }
  .disc { margin-top:12px; padding-top:7px; border-top:1px solid #cdd9cd; font-size:7.5pt; color:#5a6b5a; line-height:1.5; }
  .status { position:fixed; left:0; right:0; top:45%; text-align:center; font-size:11pt; color:#5a6b5a; }
  @media print { .status { display:none; } }
  .ramp { display:flex; gap:14px; align-items:center; flex-wrap:wrap; font-size:7.5pt; color:#5a6b5a; margin-top:5px; }
  .rampitem { display:inline-flex; align-items:center; gap:4px; }
  .bar { display:inline-block; width:56px; height:7px; border-radius:2px; border:1px solid #cdd9cd;
         background:linear-gradient(90deg,#3b4cc0,#7aa8d8,#f2f0a0,#ec9a58,#b40426); }
</style></head><body>
<div class="status" id="status">Loading imagery…</div>

<section class="page">
  <header>
    <div><h1>${esc(meta.farmer)}</h1>
         <div class="sub">${esc(meta.year)} planting report — for FSA acreage reporting</div>
         <div class="ident">${idLine(rows)}</div>
         ${meta.crops ? `<div class="sub"><b>${esc(meta.crops.join(", "))} only</b> — this is not the whole farm</div>` : ""}</div>
    <div class="meta">Clover Ag Risk Management<br>(585) 233-1066<br><span class="dim">${esc(today)}</span></div>
  </header>
  <div class="kpis">
    <div class="kpi"><b>${ac(s.planted_acres)}</b><div>PLANTED ACRES MAPPED</div></div>
    <div class="kpi"><b>${s.fields}</b><div>FSA FIELDS</div></div>
    ${s.unmapped_acres > 0.5 ? `<div class="kpi"><b style="color:#8c3a3a">${ac(s.unmapped_acres)}</b><div>NOT MAPPED</div></div>` : ""}
    ${s.no_coverage_acres > 0.5 ? `<div class="kpi"><b style="color:#8a6d1f">${ac(s.no_coverage_acres)}</b><div>NO COVERAGE MAP YET</div></div>` : ""}
  </div>
  ${flags.length ? `<div class="attn"><h3>Review before submitting</h3><ul>${flags.map(f => `<li>${f}</li>`).join("")}</ul></div>` : ""}
  <h2>Planted acres by Farm-Tract-Field</h2>
  <table>
    <thead><tr><th>Farm-Tract-Field</th><th>County</th><th>St</th><th>Crop</th><th class="r">Planted ac</th>
      <th>Plant date</th><th class="r">Seed rate</th><th class="r">Policy ac</th><th>Policy</th></tr></thead>
    <tbody>${tableRows}</tbody>
    <tfoot><tr><td>${rows.length} field${rows.length === 1 ? "" : "s"}</td><td></td><td></td><td></td>
      <td class="r">${ac(s.planted_acres)}</td><td></td><td></td><td></td><td></td></tr></tfoot>
  </table>
  <div class="disc">
    <b>Planted acres are measured from the planter's own coverage</b> within each FSA field, and are what the
    machine recorded — not an acreage determination. They differ from a measured or FSA-determined acreage
    through point rows, overlap, headlands and skips. Plant dates are the machine's record of when the planter
    ran. <b>Prevent plant is not shown</b> — it has not been declared at this stage and is the grower's to report.
    Where a policy acreage already exists for the field it is shown for comparison only; it is blank when the
    acreage report for this crop year has not been keyed yet.
    This is an agent-prepared summary from John Deere Operations Center data — verify with your FSA office
    before submitting.
  </div>
</section>

${(exUn.length || exNo.length) ? `
<section class="page">
  <header>
    <div><h1>${esc(meta.farmer)}</h1>
         <div class="sub">${esc(meta.year)} planting — exceptions: planted ground not on a mapped FSA field</div>
         <!-- ⚠ SCOPE, NOT ATTRIBUTION. These acres are by definition on no policy
              field, so naming a policy beside them would claim the very thing the
              page exists to say we cannot claim. It names what the REPORT covers,
              so the sheet is still attributable once separated. -->
         <div class="ident">Report covers: ${idLine(rows)}</div></div>
    <div class="meta">Clover Ag Risk Management<br><span class="dim">${esc(today)}</span></div>
  </header>
  ${exUn.length ? `
    <h2>Planted, but outside every mapped field — ${ac(s.unmapped_acres)} ac</h2>
    <div class="disc" style="margin:0 0 6px;border:none;padding:0">
      These acres were planted but fall outside every Farm-Tract-Field boundary we hold. Usually this is
      <b>ground taken on since the last acreage report</b> — it needs adding at the FSA office so a CLU exists.
      Acres shown are the planter's coverage, not a determination.
    </div>
    <table>
      <thead><tr><th>Machine field name</th><th>Crop</th><th>Plant date</th>
        <th class="r">Machine ac</th><th class="r">On mapped fields</th><th class="r">Not mapped</th></tr></thead>
      <tbody>${exRows(exUn)}</tbody>
    </table>` : ""}
  ${exNo.length ? `
    <h2 style="margin-top:14px">No coverage map built yet — ${ac(s.no_coverage_acres)} ac</h2>
    <div class="disc" style="margin:0 0 6px;border:none;padding:0">
      We hold the planting record for these acres but have not yet built their coverage map, so they cannot be
      placed on a field. <b>This is our processing, not a gap in the grower's boundaries</b> — it does not mean
      the ground is unmapped, and it is listed only so the acres are accounted for.
    </div>
    <table>
      <thead><tr><th>Machine field name</th><th>Crop</th><th>Plant date</th>
        <th class="r">Machine ac</th><th class="r">On mapped fields</th><th class="r">Unplaced</th></tr></thead>
      <tbody>${exRows(exNo)}</tbody>
    </table>` : ""}
</section>` : ""}

${mapPages}

<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<script>
(function () {
  var PAGES = ${JSON.stringify(payload)};
  var TILE = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
  var done = false;
  function go(){ if(done) return; done = true;
    var s=document.getElementById("status"); if(s) s.style.display="none";
    setTimeout(function(){ window.print(); }, 250); }
  if (!window.L) { go(); return; }
  var waits = PAGES.map(function (pg, i) {
    return new Promise(function (resolve) {
      var el = document.getElementById("m" + i); if (!el) return resolve();
      var map = L.map(el, { zoomControl:false, attributionControl:false, dragging:false,
                            scrollWheelZoom:false, keyboard:false });
      var tiles = L.tileLayer(TILE, { maxZoom:19 }).addTo(map);
      // AS-PLANTED MACHINE COVERAGE, under the boundaries so the field outline still
      // reads. Pre-rasterised in the parent — these are plain <img>, so they are
      // painted by the time the tile gate fires.
      (pg.rasters || []).forEach(function (r) {
        var ov = L.imageOverlay(r.url, r.bounds, { opacity:0.9, interactive:false });
        ov.addTo(map);
        var el = ov.getElement(); if (el) el.style.imageRendering = "pixelated";
      });
      pg.feats.forEach(function (ft) {
        L.geoJSON({ type:"Feature", geometry: ft.geometry }, { style: {
          color:"#ffffff", weight:1.8, opacity:0.95,
          fill: (pg.rasters && pg.rasters.length) ? false : true,
          fillColor: ft.color, fillOpacity:0.5 } }).addTo(map);
      });
      var b = pg.bbox;
      map.fitBounds([[b.minLat,b.minLon],[b.maxLat,b.maxLon]], { padding:[16,16], maxZoom:17 });
      pg.feats.forEach(function (ft) {
        try { var c = L.geoJSON({ type:"Feature", geometry: ft.geometry }).getBounds().getCenter();
          L.marker(c, { interactive:false, icon: L.divIcon({ className:"",
            html:'<div class="lbl">'+ft.n+'</div>', iconSize:[22,12], iconAnchor:[11,6] }) }).addTo(map);
        } catch(e){} });
      map.invalidateSize();
      var settled=false, finish=function(){ if(!settled){ settled=true; resolve(); } };
      tiles.on("load", finish); setTimeout(finish, 12000);
    });
  });
  Promise.all(waits).then(function(){ setTimeout(go, 400); });
  setTimeout(go, 20000);
})();
</script>
</body></html>`;
}

const FIELD_CROP_COLORS = {
  CORN:"#ffd21f", SOYBEANS:"#54c24a", WHEAT:"#e08b2a", "WINTER WHEAT":"#e08b2a",
  "DRY BEANS":"#c2703a", OATS:"#cdd84e", BARLEY:"#d6a93e", TRITICALE:"#9aa85a",
  RYE:"#b0a060", "GRAIN SORGHUM":"#caa14a",
  // Non-grain / specialty — gave these their own colors so they stop falling back
  // to the red fallback and getting confused with wheat's orange.
  PRF:"#3f8f7a",            // forage/pasture — teal-green
  GRAPES:"#7a4a8a",         // purple
  APPLES:"#c0392b", PEACHES:"#e88f6a", CHERRIES:"#a83246", PEARS:"#9aa83a",
  POTATOES:"#8a6a3a", CABBAGE:"#5a8f6a", "PROCESSING BEANS":"#a85a2a", "SWEET CORN":"#e8c54a",
};
// Corn silage (type 026=GSS) vs grain corn (016=GSG). Silage was a bronze-gold
// #c98a2c, which is FOUR ΔE from wheat's #e08b2a — indistinguishable to normal
// vision (the floor is 15) and 1.8 under protanopia, i.e. the same colour. It went
// unnoticed only because silage rarely rendered with its type until the type
// plumbing was fixed; then a dairy's silage and its wheat drew as one crop.
// #7a4a2a is 26.2 from wheat and 43.7 from grain corn, measured, and its nearest
// neighbour anywhere in the palette is potatoes at 9.3 — a specialty crop that does
// not share a map with silage. Don't eyeball a replacement; the dataviz validator
// scores these.
// Untyped corn gets a third, muted colour — it is NOT grain, see cornTypeLabel.
const CORN_TYPE_UNTYPED = "Type not reported";
// ⚠ A FIELD RMA INSURED NOTHING ON IS STILL THE GROWER'S FIELD.
// Kevin, 2026-09-10: "Show the fields on map that are planned but make them a
// different color if they are truly uninsurable." Both halves matter — hiding
// the field reads as ground the farm does not work, and colouring it like a crop
// claims coverage that does not exist.
//
// P11 f[22] names the acres (S/U/A/D/H/V/W); both syncs now carry the figure onto
// the field row as `ui_acres`. `acres` is max(insurable base sum, uninsurable
// max), so a field with NO insurable acres has the two equal — which is the test.
// Measured CY2026 before this shipped: 35 fields carry some uninsurable acreage
// and 18 are entirely uninsurable (253.9 ac — RICE, J RICE, ED BERWANGER,
// LYNN-ETTE, HIDDEN SPRINGS), and every one of those 18 has an insurable sum of
// ZERO, so the test names no field that carries insured ground.
//
// NULL is "none recorded", not zero: the column is NULL on every row until both
// syncs re-run, and this must read as "not uninsurable" throughout that window.
const UNINSURABLE_FILL = "#8892a0";      // slate — deliberately not a crop colour
const fieldIsUninsurable = (uiAc, totalAc) => {
  const ui = Number(uiAc), ac = Number(totalAc);
  if (!(ui > 0) || !(ac > 0)) return false;
  return ui >= ac - 0.05;                // no insurable acres left on the field
};

const fieldCropColor = (c, typeCode) => {
  const cu = (c || "").toUpperCase();
  if (cu === "CORN") {
    const t = String(typeCode == null ? "" : typeCode).trim();
    if (t === "026") return "#7a4a2a";                 // GSS silage — dark forage brown
    if (!t)          return "#bfae6a";                 // type not reported — muted gold
  }
  return FIELD_CROP_COLORS[cu] || "#e34a4a";
};
// THE one corn grain/silage rule. Returns null for non-corn (so the filter leaves
// every other crop alone), a label for corn.
//
// A missing type_code is NOT grain. This used to read `=== "026" ? Silage : Grain`,
// which asserted grain for 1,307 CY2026 corn fields / 23,692 acres across 25 farmers
// that the AIP never typed, and — worse — hid them from the "Silage (GSS)" filter,
// the one question this control exists to answer. An unknown type must read unknown
// (CLAUDE.md). Codes other than 016/026 render RAW rather than folding into grain,
// the same rule the claim stage codes follow.
// ── BUG-071 — JOHN DEERE'S OWN PLACEHOLDER, READ RATHER THAN RENDERED RAW ──
//
// `UNSPECIFIED_CROP` is not an unknown code: it is the string John Deere sends when the
// OPERATOR never set a crop on the operation. Measured 2026-10-06: 240 live operations,
// 2,657 acres, all LAWNEL FARMS 2 LLC, across the 2018 / 2019 / 2025 / 2026 seasons —
// plus `NONE` as a second placeholder in the same column.
//
// ⚠ THE TOKEN STAYS IN THE DATA. Nothing here reclassifies the operation, and no crop is
// invented: those acres are counted as a known insured crop nowhere (FSA side `false` on
// both affected farms, no Claim Watch area, 0 rows in the FSA planting report). What was
// wrong is only that a raw source token was printed where a reader expects a crop name.
// Naming a placeholder the source DEFINES is a reading of it, not a guess — the same
// distinction cornTypeLabel draws below, where an absent type reads "not reported" and an
// unrecognised CODE still renders raw.
//
// Anything that is not a known placeholder passes through UNCHANGED, so this is a no-op on
// insurance and FSA crops and can be applied at every crop render site without having to
// classify each one by its source first.
const JD_CROP_PLACEHOLDERS = Object.freeze(["UNSPECIFIED_CROP", "NONE", "---"]);
const JD_CROP_UNSET = "Crop not set in John Deere";
const jdCropLabel = (crop, dash = "—") => {
  const t = String(crop == null ? "" : crop).trim();
  if (!t) return dash;
  return JD_CROP_PLACEHOLDERS.includes(t.toUpperCase()) ? JD_CROP_UNSET : crop;
};

const cornTypeLabel = (r) => {
  if (String((r && r.crop) || "").toUpperCase() !== "CORN") return null;
  const t = String((r && r.type_code) == null ? "" : r.type_code).trim();
  if (!t)          return CORN_TYPE_UNTYPED;
  if (t === "016") return "Grain (GSG)";
  if (t === "026") return "Silage (GSS)";
  return `Type ${t}`;
};

// Prevent plant is reported per COVERAGE by the AIP M13 feed (crop_coverage.pp_acres,
// from P11 f[37]='P' units), NOT per field — there is no field-level PP flag. Earlier
// code inferred PP from "field has acres but no planting date"; that heuristic was wrong
// (it missed R&H PP entirely and mislabeled unrelated no-date fields), so it was removed.
// PP acres are surfaced per county in the Insurance Summary; never guess at the field level.

// Lazily inject an SVG <pattern> (diagonal hatch over the crop color) into a
// Leaflet renderer's <svg> so prevent-plant polygons can be filled with it. Returns
// the pattern id to use as `fill="url(#id)"`. Idempotent per (svg, color).
function ensureFieldHatch(svg, color) {
  const id = "fldpp_" + String(color).replace(/[^a-z0-9]/gi, "");
  if (!svg || svg.querySelector("#" + id)) return id;
  let defs = svg.querySelector("defs");
  if (!defs) { defs = document.createElementNS("http://www.w3.org/2000/svg", "defs"); svg.insertBefore(defs, svg.firstChild); }
  const NS = "http://www.w3.org/2000/svg";
  const pat = document.createElementNS(NS, "pattern");
  pat.setAttribute("id", id); pat.setAttribute("patternUnits", "userSpaceOnUse");
  pat.setAttribute("width", "8"); pat.setAttribute("height", "8"); pat.setAttribute("patternTransform", "rotate(45)");
  const bg = document.createElementNS(NS, "rect");
  bg.setAttribute("width", "8"); bg.setAttribute("height", "8"); bg.setAttribute("fill", color); bg.setAttribute("fill-opacity", "0.55");
  const ln = document.createElementNS(NS, "line");
  ln.setAttribute("x1", "0"); ln.setAttribute("y1", "0"); ln.setAttribute("x2", "0"); ln.setAttribute("y2", "8");
  ln.setAttribute("stroke", "#ffffff"); ln.setAttribute("stroke-width", "3"); ln.setAttribute("stroke-opacity", "0.95");
  pat.appendChild(bg); pat.appendChild(ln); defs.appendChild(pat);
  return id;
}

// Multi-crop CLU fill: bold alternating bands of each crop's color. Deliberately
// distinct from the prevent-plant hatch (that's ONE crop color + fine WHITE lines
// at +45° under a DASHED border) — this is TWO+ saturated crop colors, coarse
// bands, at −45°, under a SOLID border. Reads as "split-planted field", not PP.
function ensureCropStripes(svg, colors) {
  const key = colors.map(c => String(c).replace(/[^a-z0-9]/gi, "")).join("_");
  const id = "fldmix_" + key;
  if (!svg || svg.querySelector("#" + id)) return id;
  let defs = svg.querySelector("defs");
  if (!defs) { defs = document.createElementNS("http://www.w3.org/2000/svg", "defs"); svg.insertBefore(defs, svg.firstChild); }
  const NS = "http://www.w3.org/2000/svg";
  const band = 11, n = Math.max(1, colors.length), W = band * n;
  const pat = document.createElementNS(NS, "pattern");
  pat.setAttribute("id", id); pat.setAttribute("patternUnits", "userSpaceOnUse");
  pat.setAttribute("width", String(W)); pat.setAttribute("height", "12"); pat.setAttribute("patternTransform", "rotate(-45)");
  colors.forEach((c, i) => {
    const r = document.createElementNS(NS, "rect");
    r.setAttribute("x", String(i * band)); r.setAttribute("y", "0");
    r.setAttribute("width", String(band + 0.5)); r.setAttribute("height", "12");   // +0.5 avoids hairline gaps
    r.setAttribute("fill", c); r.setAttribute("fill-opacity", "0.82");
    pat.appendChild(r);
  });
  defs.appendChild(pat);
  return id;
}

// ── Field-boundary cache ─────────────────
// field_geometries is ~16 MB and near-STATIC — it only changes when new boundary
// files are loaded (the daily sync never touches it). Re-downloading it on every
// map open is the main reason the Field Mapper got slower as the boundary library
// grew. So cache the whole boundary map in memory (survives tab navigation) and
// re-fetch ONLY when the library actually changed, detected by a one-row fingerprint
// (row count + newest updated_at). policy_fields (which fields are keyed) is still
// fetched fresh every open, so a freshly-keyed field shows up immediately, drawn
// against its already-cached boundary — no 16 MB re-download.
let _geomCache = { fp: null, map: null };
async function _fieldGeomFingerprint() {
  const res = await authedFetch(`${SUPABASE_URL}/rest/v1/field_geometries?select=updated_at&order=updated_at.desc.nullslast&limit=1`,
    { headers: { Prefer: "count=exact", "Range-Unit": "items", "Range": "0-0" } });
  if (!res.ok) throw new Error(`fingerprint ${res.status}`);
  const total = (res.headers.get("content-range") || "").split("/")[1] || "?";
  const rows = await res.json().catch(() => []);
  return `${total}|${rows[0]?.updated_at || ""}`;
}
// Ask for ONLY the boundaries the caller can actually draw, when that's a short
// list. The whole library is 22k polygons / 18 MB — fine to cache once for an
// agent looking at the book, ruinous for a portal grower with forty fields on a
// phone, and pure waste in admin "preview as" (the admin session bypasses RLS,
// so it downloaded all 18 MB to render one farm's fields). Both were the reason
// the Field Mapper hung or fell over on the portal side.
//
// Chunked because a PostgREST `in.(…)` list goes in the URL. Above the cutoff
// there's no saving left and we fall back to the cached whole-library load.
const GEOM_SCOPED_MAX = 3000;
// ⚠ THE ONE EXPRESSION FOR JOINING A CLU TO field_geometries, because two hand-rolled copies
// drifted and both were wrong in the same direction.
//
// `field_geometries.clu_id` is LOWERCASE on every row, and that is an invariant set at the
// writer — api/_geomload.js and api/_geomupload.js both lowercase before insert — not a
// coincidence of the current data. `policy_fields.clu_id` is MIXED: 12,347 lower and 17,673
// UPPER of 30,155 rows (measured 2026-10-01). PostgREST `in.()` and `eq.` match the exact
// string, so a query built from the policy_fields spelling returns nothing for 59% of fields —
// and the field then renders as "no boundary on file", which reads as a fact about the farm.
//
// Measured on the two sites that rolled their own: the PRF coverage map drew 6,452 of the
// 15,806 CY2026 fields it holds geometry for, 209 of 214 farms short, and 13 farms rendered
// COMPLETELY EMPTY. Fold BOTH sides — the query and the map key — or neither half works.
const cluLower = (v) => String(v == null ? "" : v).trim().toLowerCase();
async function loadFieldGeomsFor(cluIds) {
  const want = [...new Set((cluIds || []).map(cluLower).filter(Boolean))];
  if (!want.length || want.length > GEOM_SCOPED_MAX) return loadFieldGeomsCached();
  // Chunks go out TOGETHER, not one after the next. Each chunk is an independent
  // round trip, so awaiting them in sequence just added a farm's worth of latency
  // to itself — the grower with the most fields waited the longest for no reason.
  const chunks = [];
  for (let i = 0; i < want.length; i += 200) chunks.push(want.slice(i, i + 200));
  const parts = await Promise.all(chunks.map(chunk => {
    const list = chunk.map(c => `"${c}"`).join(",");
    return sbFetch("field_geometries", {
      select: "clu_id,geometry",
      filters: [`clu_id=in.(${encodeURIComponent(list)})`],
    }).catch(() => []);
  }));
  const m = {};
  for (const g of parts) for (const r of (g || [])) if (r.clu_id) m[String(r.clu_id).toLowerCase()] = r.geometry;
  return m;
}
// One download at a time. The Field Mapper now STARTS this the moment an agent
// opens the page (see FieldDataView), in parallel with the field inventory, and the
// map asks for it again once the inventory lands. Without sharing the promise that
// second ask would start a second 17 MB download of the same library.
let _geomInflight = null;
function loadFieldGeomsCached() {
  if (_geomInflight) return _geomInflight;
  _geomInflight = (async () => {
    let fp = null;
    try { fp = await _fieldGeomFingerprint(); } catch { fp = null; }
    if (fp && _geomCache.fp === fp && _geomCache.map) return _geomCache.map;   // library unchanged → instant
    const g = await sbFetch("field_geometries", { select: "clu_id,geometry" });
    // Lower-cased on the way in — every lookup downstream keys on lower(clu_id).
    const m = {}; for (const r of (g || [])) if (r.clu_id) m[String(r.clu_id).toLowerCase()] = r.geometry;
    if (fp) _geomCache = { fp, map: m };
    return m;
  })();
  // Cleared on settle either way, so a failed read is retried next time and a
  // later call re-checks the fingerprint instead of reusing a stale promise.
  _geomInflight.then(() => { _geomInflight = null; }, () => { _geomInflight = null; });
  return _geomInflight;
}

// Sub-field yield heatmap ramp — RdYlGn 5-stop, matching John Deere's yield maps
// (dark red → orange → yellow → green → dark green), clamped to the [lo,hi] domain.
const _JD_RAMP = [[165, 0, 38], [244, 109, 67], [255, 255, 140], [102, 189, 99], [0, 104, 55]];
// Whole-operation comparison ramp — RdYlGn plus a dark-purple top stop so the
// operation's very best spots stand out from merely-good green. Fixed scale
// across every field of a crop lets you compare fields directly.
const _JD_OPS_RAMP = [[120, 0, 20], [214, 47, 39], [253, 141, 60], [255, 255, 150], [120, 198, 121], [35, 132, 67], [0, 90, 50], [84, 39, 143]];
function jdOpsColor(v, lo, hi) {
  if (!(hi > lo)) hi = lo + 1;
  let t = (v - lo) / (hi - lo); t = Math.max(0, Math.min(1, t));
  const seg = t * (_JD_OPS_RAMP.length - 1), i = Math.min(_JD_OPS_RAMP.length - 2, Math.floor(seg)), f = seg - i;
  const a = _JD_OPS_RAMP[i], b = _JD_OPS_RAMP[i + 1];
  return `rgb(${Math.round(a[0] + f * (b[0] - a[0]))},${Math.round(a[1] + f * (b[1] - a[1]))},${Math.round(a[2] + f * (b[2] - a[2]))})`;
}
function jdYieldColor(v, lo, hi) {
  if (!(hi > lo)) hi = lo + 1;
  let t = (v - lo) / (hi - lo); t = Math.max(0, Math.min(1, t));
  const seg = t * (_JD_RAMP.length - 1), i = Math.min(_JD_RAMP.length - 2, Math.floor(seg)), f = seg - i;
  const a = _JD_RAMP[i], b = _JD_RAMP[i + 1];
  return `rgb(${Math.round(a[0] + f * (b[0] - a[0]))},${Math.round(a[1] + f * (b[1] - a[1]))},${Math.round(a[2] + f * (b[2] - a[2]))})`;
}
// ── SOIL SAMPLES ARE PARKED ─────────────────────────────────────────────────
// PARKED — OUTSIDE CURRENT CORE. Kevin, 2026-08-24: soil sampling sits outside
// what Silas is for, which is field data -> production outlook -> crop insurance
// -> farm economics -> marketing -> action. It is NOT "agency only": that would
// be a statement about who may see it, and this is a statement about what the
// product is doing this year.
//
// PARKED, NOT DELETED, and the distinction is the whole instruction. Everything
// that took work to build is untouched and one line from returning:
//   • the tables `soil_sample_events` and `soil_samples`, and their rows;
//   • their RLS policies, and the rls_selftest() cases that prove them — those
//     still run and still pass, because the data is still there;
//   • tools/load-soil-shapefile.js, the only ingest path;
//   • every renderer, ramp and rollup below.
//
// A FLAG RATHER THAN AN EXCISION, deliberately. Cutting ~400 lines out of a
// 95,000-line file with no test suite is exactly the kind of change that breaks
// something else in front of a customer, which is the failure mode we are now
// explicitly trying to stop. Three gates take it off every product surface — the
// map loader, the nav tab and the screen — and flipping this to false brings all
// of it back at once.
const SOIL_SAMPLES_PARKED = true;

// ── SOIL SAMPLE RENDERING ───────────────────────────────────────────────────
// Which analytes the map can draw, and how each one reads. `good` says which end of
// the range is agronomically good, because the ramp must not imply "more is better"
// for a value where it isn't — pH is a target band, not a slope.
const SOIL_ANALYTES = [
  { key: "PH",    label: "pH",             kind: "band",  lo: 5.2, hi: 7.0, target: [6.2, 6.8], dp: 1 },
  { key: "OM",    label: "Organic matter", kind: "ramp",  unit: "%",  dp: 1 },
  { key: "P",     label: "Phosphorus",     kind: "ramp",  dp: 0 },
  { key: "K",     label: "Potassium",      kind: "ramp",  dp: 0 },
  { key: "MG",    label: "Magnesium",      kind: "ramp",  dp: 0 },
  { key: "CA",    label: "Calcium",        kind: "ramp",  dp: 0 },
  { key: "CEC",   label: "CEC",            kind: "ramp",  unit: "meq/100g", dp: 1 },
  { key: "BS_K",  label: "Base sat — K",   kind: "ramp",  unit: "%",  dp: 1 },
  { key: "BS_MG", label: "Base sat — Mg",  kind: "ramp",  unit: "%",  dp: 1 },
  { key: "BS_CA", label: "Base sat — Ca",  kind: "ramp",  unit: "%",  dp: 1 },
];
const soilAnalyteDef = (k) => SOIL_ANALYTES.find(a => a.key === k) || SOIL_ANALYTES[0];
// Sequential ramp for "more is more" analytes — pale straw → deep green. Deliberately
// NOT the yield ramp: a soil map and a yield map on the same screen must not be
// mistaken for each other.
const _SOIL_RAMP = [[241,236,214],[214,226,178],[176,208,146],[130,184,120],[86,156,99],[48,124,80],[22,92,62]];
// Divergent ramp for pH — red (acid) through green (in band) to blue (alkaline), the
// soil-test-strip vernacular every grower already reads.
const _PH_RAMP = [[168,58,42],[201,110,58],[226,175,88],[126,170,96],[74,142,96],[86,140,168],[62,98,150]];
function soilColor(v, lo, hi, kind) {
  if (v == null || !Number.isFinite(v)) return "#60735f";     // no value ≠ a low value
  if (!(hi > lo)) hi = lo + 1;
  const ramp = kind === "band" ? _PH_RAMP : _SOIL_RAMP;
  let t = (v - lo) / (hi - lo); t = Math.max(0, Math.min(1, t));
  const seg = t * (ramp.length - 1), i = Math.min(ramp.length - 2, Math.floor(seg)), f = seg - i;
  const a = ramp[i], b = ramp[i + 1];
  return `rgb(${Math.round(a[0] + f * (b[0] - a[0]))},${Math.round(a[1] + f * (b[1] - a[1]))},${Math.round(a[2] + f * (b[2] - a[2]))})`;
}
// Build the sampling work list. Takes a NORMALISED field list so the map view (which
// has GeoJSON features) and the Soil Samples tab (which has policy rows) share one
// implementation and cannot drift — the arity IS the contract, per check-shared-rules.
//   fields: [{ clu, name, named, acres, crop }]
//   events: soil_sample_events rows (need clu_id, sampled_on, centroid_lat, …)
// A field with NO sampling is the most important row here, so it sorts first and is
// never an absence.
function buildSoilTracker(fields, events, intervalYrs) {
  const byClu = new Map();
  for (const ev of (events || [])) {
    const k = String(ev.clu_id || "").toLowerCase();
    if (!k) continue;                                   // unmatched — reported separately
    if (!byClu.has(k)) byClu.set(k, []);
    byClu.get(k).push(ev);
  }
  const today = new Date();
  const seen = new Set();
  const rows = [];
  for (const f of (fields || [])) {
    const clu = String(f.clu || "").toLowerCase();
    if (!clu || seen.has(clu)) continue;
    seen.add(clu);
    const evs = (byClu.get(clu) || []).slice().sort((a, b) => String(b.sampled_on).localeCompare(String(a.sampled_on)));
    const last = evs[0] || null;
    const ageYrs = last ? (today - new Date(last.sampled_on + "T00:00:00Z")) / (365.25 * 864e5) : null;
    rows.push({ ...f, clu, events: evs, last, ageYrs,
      status: !last ? "never" : (ageYrs > intervalYrs ? "overdue" : "current") });
  }
  rows.sort((a, b) => {
    if (!a.last !== !b.last) return a.last ? 1 : -1;
    if (!a.last && !b.last) return (b.acres || 0) - (a.acres || 0);
    return String(a.last.sampled_on).localeCompare(String(b.last.sampled_on));
  });
  return {
    rows,
    orphans: (events || []).filter(e => !e.clu_id),
    never: rows.filter(r => r.status === "never").length,
    overdue: rows.filter(r => r.status === "overdue").length,
    current: rows.filter(r => r.status === "current").length,
    acresNever: rows.filter(r => r.status === "never").reduce((s, r) => s + (r.acres || 0), 0),
  };
}

// Read one analyte off a sample's `results` jsonb. Returns the whole record, not just
// the number, because unit / extraction / basis travel WITH the value everywhere —
// an analyte the lab never ran is absent, and absent must never render as 0.
function soilVal(sample, key) {
  const r = sample && sample.results && sample.results[key];
  if (!r || r.v == null || !Number.isFinite(Number(r.v))) return null;
  return { v: Number(r.v), unit: r.unit || "", ext: r.ext || null, basis: r.basis || "measured", note: r.note || null };
}
// Fill a sparse yield grid into full coverage (John Deere renders the swath area
// solid; our 2 m binning of a thin point cloud leaves most cells empty → a gappy
// map). Iterative nearest-value dilation: grow filled cells into empty neighbors,
// up to `passes` cells out, so between-swath and along-swath gaps close into a
// continuous surface WITHOUT bleeding far past the harvested area (bounded growth).
// Optionally smooth (contour look) once filled. Cached on the grid object by key.
function jdFillGrid(values, nx, ny, passes, smooth) {
  let v = values.slice();
  for (let p = 0; p < passes; p++) {
    let changed = false; const nv = v.slice();
    for (let row = 0; row < ny; row++) for (let col = 0; col < nx; col++) {
      const i = row * nx + col; if (v[i] != null) continue;
      let s = 0, c = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue; const ax = col + dx, ay = row + dy;
        if (ax < 0 || ay < 0 || ax >= nx || ay >= ny) continue;
        const j = ay * nx + ax; if (v[j] != null) { s += v[j]; c++; }
      }
      if (c > 0) { nv[i] = s / c; changed = true; }
    }
    v = nv; if (!changed) break;
  }
  // `smooth` = number of 3x3 averaging passes over filled cells for a contour
  // look. Raw combine yield data is very noisy point-to-point (flow surges,
  // moisture, GPS jitter); a few passes read like John Deere's smoothed maps
  // without washing out the real high/low zones. 0 = raw pixels.
  for (let sp = 0; sp < smooth; sp++) {
    const nv = v.slice();
    for (let row = 0; row < ny; row++) for (let col = 0; col < nx; col++) {
      const i = row * nx + col; if (v[i] == null) continue;
      let s = 0, c = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const ax = col + dx, ay = row + dy; if (ax < 0 || ay < 0 || ax >= nx || ay >= ny) continue;
        const j = ay * nx + ax; if (v[j] != null) { s += v[j]; c++; }
      }
      if (c) nv[i] = s / c;
    }
    v = nv;
  }
  return v;
}

// ── Yield-map colour scales ──────────────────────────────────────────────────
// A scale has to be read off the SURFACE THAT GETS DRAWN, never the raw monitor
// cells. Raw combine data carries start/stop passes and partial-width header
// cuts: Sullivan's 2024 corn pools to p02 = 36.2 and p98 = 247.1 bu/ac across 27
// fields that AVERAGE 174–207, while the middle half of all cells sits between
// 161 and 222. Stretching a 14-band ramp over 36→247 spends most of its colour
// on noise that smoothing deletes before anything is rendered, so every field
// landed in the top band or two and drew as ONE SOLID BLOCK — the compare view
// with no sub-field detail left in it. jdFillGrid's smoothing removes exactly
// that noise, so the percentiles are taken after it.
//
// The filled surface is cached on the grid object: it's ~13 full sweeps of the
// array (1.2M cells on a big field) and compare draws 27 fields at once.
function jdFilledValues(g, smooth = 3) {
  if (!g || !g.values || !g.nx || !g.ny) return null;
  if (g._filled == null || g._fillSmooth !== smooth) {
    g._filled = jdFillGrid(g.values, g.nx, g.ny, 10, smooth);
    g._fillSmooth = smooth;
    g._fillSorted = null;
  }
  return g._filled;
}
// Sorted non-null surface values — the basis for every percentile below. Cached
// alongside the surface, since sorting a million cells is not free either.
function jdSurfaceSorted(g, smooth = 3) {
  const v = jdFilledValues(g, smooth);
  if (!v) return [];
  if (!g._fillSorted) {
    const out = [];
    for (const x of v) if (x != null) out.push(x);
    out.sort((a, b) => a - b);
    g._fillSorted = out;
  }
  return g._fillSorted;
}
const jdPctOf = (sorted, p) => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] : 0);
function jdFieldScaleOf(g, smooth = 3) {
  const s = jdSurfaceSorted(g, smooth);
  if (!s.length) return null;
  return { lo: +jdPctOf(s, 0.02).toFixed(1), hi: +jdPctOf(s, 0.98).toFixed(1) };
}
// One scale across several fields, each weighted EQUALLY. Pooling cells straight
// would let a 300-acre field own the ramp while a 20-acre field barely moves it —
// and the ramp is the whole basis of the comparison.
function jdSharedScaleOf(grids, smooth = 3, per = 2000) {
  const pool = [];
  for (const g of (grids || [])) {
    const s = jdSurfaceSorted(g, smooth);
    if (!s.length) continue;
    const n = Math.min(per, s.length), step = s.length / n;
    for (let i = 0; i < n; i++) pool.push(s[Math.min(s.length - 1, Math.floor(i * step))]);
  }
  if (!pool.length) return null;
  pool.sort((a, b) => a - b);
  return { lo: +jdPctOf(pool, 0.02).toFixed(1), hi: +jdPctOf(pool, 0.98).toFixed(1) };
}

// ── COMPARE IDENTITY ──────────────────────────────────────────────────────────
// What makes two JD grids comparable: the same METRIC, the same CROP, the same
// YEAR — and the same UNIT. The unit is the half that was missing, and it is the
// half the metric cannot supply: seed_rate arrives as seeds/ac on 9,848 grids and
// lb/ac on 1,917, and yield as bu/ac or (corn silage) ton/ac. Keyed without it,
// Austic Farm Partners' SOYBEANS 2024 put 38 grids at 44.9–77.5 lb/ac on ONE
// shared ramp with 95 grids at 92,908–179,523 seeds/ac: every pound-rate field
// painted flat at the bottom of the scale as if it had failed, the operation
// average was an acre-weighted blend of pounds and seeds, and the unit stamped
// beside it was whichever grid happened to sort first. 34 combos across 11 farms
// were in that state on 2026-08-22.
//
// ONE FUNCTION, TWO CALLERS — the combo list and the grid filter. They were two
// copies of the same template literal, which is how a key drifts: change one and
// the picker offers a combo the filter can never populate.
//
// A NULL UNIT IS ITS OWN BUCKET, never folded into a known one. Those are grids
// written before the column existed; "no label" is not evidence that the label
// would have matched, and merging them is the same unproven assumption the
// defect above was made of. They are shown, and named as unrecorded.
// TWO KEYS, AND THEY ARE NOT THE SAME KEY. /api/market?fn=jd-opsgrids takes a
// metric, a crop and a year — it has no unit parameter, and one request returns
// BOTH units for that crop-year. So the key that decides what to DOWNLOAD is
// three-part, while the key that decides what may share a SCALE is four-part.
//
// Keeping them as one string is what broke the moment the unit was added: the
// pixel loader compared its own hand-rolled three-part literal against mapCombo,
// which had quietly become four-part, so it matched nothing, downloaded nothing,
// and Compare drew an empty map. Building one key out of the other makes that
// relationship the code, rather than a coincidence two literals must maintain.
function jdGridFetchKey(g) {
  return `${g.metric}|${g.crop || "?"}|${g.year || ""}`;
}
function jdComboKey(g) {
  return jdGridFetchKey(g) + "|" + (g.unit || "");
}

// How a combo names its unit on screen. An unrecorded unit says so rather than
// printing nothing — a bare 32534.6 over a planting map is the number without the
// one word that makes it mean anything.
function jdComboUnitLabel(c) {
  return (c && c.unit) ? c.unit : "unit not recorded";
}

// What to CALL a grid's field on screen. JD hands back a GUID where the grower
// never named the field, and the FTF label is the only human-readable thing left;
// a custom-applied pass keeps the name it arrived under. This was written out
// three times inside JdFieldDataView — the list, the field-mode footer and (as of
// the hover readout) a third place — which is how a name comes to disagree with
// itself on one screen.
function jdFieldLabel(g) {
  if (!g) return "";
  // The custom-work row used to bypass this, because the server put the operator's
  // NAME in `field`. It no longer does — `field` is the field on every row — so the
  // GUID guard applies uniformly. A foreign field we hold no name for is a raw GUID,
  // and printing one reads as a broken page rather than as an unnamed field.
  const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-/i.test(String(g.field || ""));
  return isGuid ? (g.ftf_label || "Unnamed field") : g.field;
}

// Planting-map fill color for a JD crop name (reuses CROP_COLOR; normalizes JD's
// specific names like SORGHUM / *_BEANS / TRITICALE_EURO). Cover crops → gray.
function jdCropColor(crop) {
  const c = String(crop || "").toUpperCase();
  if (/CORN/.test(c)) return CROP_COLOR.CORN;
  if (/SOY/.test(c)) return CROP_COLOR.SOYBEANS;
  if (/WHEAT/.test(c)) return CROP_COLOR.WHEAT;
  if (/SORGHUM/.test(c)) return CROP_COLOR["GRAIN SORGHUM"];
  if (/BEAN/.test(c)) return CROP_COLOR["DRY BEANS"];
  if (/TRITICALE/.test(c)) return CROP_COLOR.TRITICALE;
  if (/\bOAT/.test(c)) return CROP_COLOR.OATS;
  if (/BARLEY/.test(c)) return CROP_COLOR.BARLEY;
  return "#60735f";
}

// Phase B map — Leaflet satellite basemap + solid crop-colored field boundaries
// with a flat farm list on the left. Clicking a farm zooms the map to it.
// Boundaries come from field_geometries (CLU shapefiles) joined to the in-view
// rows on lowercased CLU_ID; R&H lat/lon fields render as circle markers.
// Sequential ramp for the Yield layer, light → dark on ONE hue. Blue rather than
// green because green already encodes CROP on this map — reusing it would make
// "high yield" and "corn" the same visual signal. Five steps, monotonically
// darkening, so the order reads without consulting the legend.


// ── John Deere yield units ───────────────────────────────────────────────────
// jd_field_ftf carries no unit column, so the unit is derived from the crop. This
// is deterministic, not a guess: silage is harvested as tonnage and everything
// else in this book is a bushel crop. RMA models silage the same way — CORN with
// type_code 026 (GSS), priced per TON ($38.25), which is why a silage figure must
// never be rendered with "bu/ac" beside it.
// Crops John Deere reports that are NOT insurable here — cover crops and uninsured
// contract vegetables. Without this a radish pass or Triple H's 85 acres of green
// beans reads as an acreage discrepancy, when the truth is there was never a policy
// to reconcile against. Kept in sync with JD_UNINSURED in api/_jd.js, which is the
// source of truth (the sync uses it to decide what NOT to normalise to an RMA crop).
const JD_UNINSURED_CROPS = new Set([
  "RETTICH", "PEA_TRAPPER", "TALL_FESCUE", "FORAGE_MIX", "CLOVER",
  "SUNFLOWER_OIL", "RAPE_SEED", "GREEN_BEANS", "PUMPKINS", "NONE",
]);
const jdCropUninsured = (crop) => JD_UNINSURED_CROPS.has(String(crop || "").trim().toUpperCase());

// Below this many sampled cells a John Deere grid has no drawable extent — the
// export came back with a stray point or two rather than a real coverage map.
const MIN_MAP_CELLS = 10;
const jdIsSilage = (crop) => /SILAGE/i.test(String(crop || ""));
// Dry beans are insured, sold and stored in POUNDS (Kevin, 2026-10-31: "Dry bean yield
// is in pounds per acre"). api/_jd.js keeps their dry_yield in lb/ac (INSURED_IN_LB).
const jdIsLbCrop = (crop) => String(crop || "").toUpperCase().trim() === "DRY BEANS";
const jdYieldUnit = (crop) => (jdIsSilage(crop) ? "ton/ac" : jdIsLbCrop(crop) ? "lb/ac" : "bu/ac");
// ⚠ AND `jdYieldUnit` IS A GUESS THAT MUST NOT OVERRIDE THE SERVER'S REFUSAL
// (BUG-061, 2026-09-11). `api/_jd.js` now returns NO unit for a forage grid whose
// basis the source never stated — 234 grids across 4 farms — precisely because
// `bu/ac` there was an invention. Every by-area render then wrote it straight back
// in as `jdYieldUnitLabel(r.crop, r.yield_unit)`, which prints "11.9 bu/ac" over
// alfalfa and "7.3 ton/ac" over silage whose figure is dry matter. The client
// cannot be the place that re-decides a question the server answered UNKNOWN.
//
// For a GRAIN crop the fallback stands: grain is bushels, universally, so a
// missing label there costs nothing and dropping it would blank thousands of
// correct rows. For forage there is no such universal — wet tons, dry tons and
// bushels are all in this book — so the honest answer is that it is not recorded.
const JD_FORAGE_CROPS = new Set(["CORN SILAGE", "SORGHUM SILAGE", "ALFALFA",
  "GRASS_FORAGE", "GRASS_ALFALFA", "CLOVER", "TALL_FESCUE", "HAYLAGE"]);
const jdIsForage = (crop) => JD_FORAGE_CROPS.has(String(crop || "").toUpperCase().trim());
// RMA insures corn silage as CORN with type_code 026 against grain's 016, so a join
// from a John Deere crop to an INSURED crop has to fold silage into CORN or every
// chopped row keys against nothing. This is `insuredCropKey` in api/_jd.js, which
// documents why CORN SILAGE is the ONLY pair that folds: it is the only JD crop
// name with zero policy_fields twins that is the SAME insured crop. ALFALFA and
// OATS also match nothing and are genuinely DIFFERENT crops on ground insured for
// something else — folding those would credit one crop's acres to another.
// ⚠ ACREAGE FOLDS; UNITS DO NOT. This is only ever for KEYING.
const jdInsuredCropKey = (c) => (String(c || "").toUpperCase() === "CORN SILAGE" ? "CORN" : c);
// The unit to PRINT, or null when nothing established one. Callers render null as
// "unit not recorded" — never as a blank, and never as a guess.
// ⚠ AND A STAMPED "bu/ac" ON FORAGE IS THE SAME GUESS ARRIVING FROM THE OTHER SIDE.
// By field takes a forage harvest's `dry_yield` as bushels when the monitor wrote one,
// and John Deere's own code for "no crop picked" (UNSPECIFIED_CROP — Lawnel 2's 21 hay
// cuttings at 259.8) carries the combine default. Neither is a bushel, so neither is
// printed as one; a ton or lb label stands.
const jdYieldUnitOrNull = (crop, unit) => { const noBu = jdIsForage(crop) || /^UNSPECIFIED/i.test(String(crop || "").trim()); return (unit && !(noBu && /^bu\s*\/\s*ac$/i.test(String(unit)))) ? unit : (noBu ? null : jdYieldUnit(crop)); };
const jdYieldUnitLabel = (crop, unit) => jdYieldUnitOrNull(crop, unit) || "unit not recorded";
const jdYieldDecimals = (crop) => (jdIsSilage(crop) ? 1 : 1);

// Physically plausible per-acre yields, by unit. Catches BOTH failure modes seen
// in this data: a silage tonnage mislabelled as bushels, and an operation logged
// with area_acres = 0 where the per-acre figure is a divide-by-zero blowup.
const jdYieldPlausible = (crop, v) => {
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return false;
  // Beans are pounds: a 2,300 lb/ac field is ordinary, and the 350 bushel ceiling hid
  // every bean yield from the map.
  return jdIsSilage(crop) ? n <= 45 : jdIsLbCrop(crop) ? n <= 6000 : n <= 350;
};
// No stored unit reaches this one, so a forage crop reports that nothing
// established its basis rather than taking the crop-derived guess.
// ⚠ A FEW OLD BEAN MAPS ARE STILL BUSHELS. 11 dry bean grids (2015-2024) were built
// before beans were stored in pounds and read 26-61. No bean field yields 150 bu/ac
// (9,000 lb), so a bean figure under 150 is bushels and is labelled as bushels rather
// than printed as "35 lb/ac".
// ⚠ AND A SMALL GRAIN UNDER 15 IS A CHOPPER'S TONNAGE. Triticale, oats, rye and barley
// are combined on some farms and chopped on others; Lawnel 2's 2026 triticale reads 2.1
// off the forage harvester, which no grain stand yields in bushels. The label says the
// unit was not recorded rather than calling it 2.1 bu/ac.
const JD_CHOPPED_SMALL_GRAINS = new Set(["TRITICALE", "OATS", "RYE", "BARLEY"]);
// A tonnage the server stamped (silage converted to wet tons at 35% dry matter,
// Kevin 2026-11-02) is printed as
// stamped, so the field card reads the same wet t/ac as By field (Kevin, 2026-11-01).
const jdYieldLabel = (crop, v, unit) => { const su = unit ? jdYieldUnitOrNull(crop, unit) : null; return su && /ton/i.test(su)
  ? `${Number(v).toFixed(jdYieldDecimals(crop))} ${jdShowUnit(su)}`
  : `${Number(v).toFixed(jdYieldDecimals(crop))} ${jdIsLbCrop(crop) && Number(v) < 150 ? "bu/ac"
    : JD_CHOPPED_SMALL_GRAINS.has(String(crop || "").toUpperCase().trim()) && Number(v) < 15 ? "unit not recorded"
    : jdYieldUnitLabel(crop, null)}`; };

// A tonnage came off a forage chopper, not a combine, so the card says so (Kevin, 2026-11-01).
const jdHarvestWord = (unit) => (unit && /ton/i.test(String(unit)) ? "chopper" : "combine");

// Which John Deere field covers a point (the centre of a clicked insured field)?
// Insured CLU boundaries and JD field boundaries are drawn by different parties
// and rarely agree edge-for-edge, so containment is tried first and a BOUNDED
// nearest-centroid fallback second — a CLU whose centre lands just outside the
// JD polygon has to still resolve, or the button reads as broken. The bound is
// what keeps a neighbouring farm's field from being picked instead.
function jdFieldAtPoint(fc, lat, lng) {
  const feats = (fc && fc.features) || [];
  if (!feats.length || !isFinite(lat) || !isFinite(lng)) return null;
  const inRing = (ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
      if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  // Even-odd across ALL of a polygon's rings, so a hole reads as outside.
  const inPoly = (rings) => (rings || []).reduce((acc, r) => (r && r.length >= 3 ? acc !== inRing(r) : acc), false);
  const polysOf = (g) => (!g ? [] : g.type === "Polygon" ? [g.coordinates] : g.type === "MultiPolygon" ? g.coordinates : []);
  const kx = Math.cos((lat * Math.PI) / 180);   // longitude degrees shrink with latitude
  let near = null, nearD = Infinity;
  for (const f of feats) {
    for (const rings of polysOf(f.geometry)) {
      if (inPoly(rings)) return f;
      const ring = (rings && rings[0]) || [];
      if (!ring.length) continue;
      let sx = 0, sy = 0;
      for (const pt of ring) { sx += pt[0]; sy += pt[1]; }
      const dx = (lng - sx / ring.length) * kx, dy = lat - sy / ring.length;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < nearD) { nearD = d; near = f; }
    }
  }
  // ~0.004° ≈ 450 m from a field's centre: close enough to be the same field,
  // tight enough that "no JD field here" stays an honest answer.
  return nearD <= 0.004 ? near : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// MY ACRES — the grower-facing verification view.
//
// The problem this replaces: growers get a Schedule of Insurance in the mail and
// nobody reads it, so acre errors survive until they matter — at a claim. This is
// the same information shaped so a farmer will actually check it: his own field
// names, grouped by crop, acres big enough to scan, and a bar so a wrong number
// looks wrong rather than merely being wrong.
//
// Deliberately NOT the agent view. No AIP, no policy numbers, no liability, no
// plan codes. The single question is "are these the right fields and acres?", and
// every extra column is a reason to stop reading.
//
// Machine yield rides along where we have it, because that's the part a grower
// will come back for — but it never competes with the acres, which are the thing
// being verified.
// ─────────────────────────────────────────────────────────────────────────────
function FieldVerifyPanel({ rows = [], labels = {}, cluYield = null, cty, fmtAc, acreOf, isMobile }) {
  const [openCrop, setOpenCrop] = useState(null);

  const nameFor = (r) => {
    const lbl = labels[String(r.clu_id || "").toLowerCase()];
    if (lbl) return lbl;
    const parts = [r.fsn, r.tract, r.field_id].filter(x => x != null && x !== "");
    return parts.length ? parts.join("-") : "(unnamed field)";
  };

  const byCrop = useMemo(() => {
    const m = new Map();
    for (const r of (rows || [])) {
      // ⚠ NOT jdCropLabel HERE — THIS IS A KEY, NOT A LABEL. It groups the map
      // (`m.get(crop)`) and rides inside a composite field key below, and these rows are
      // INSURANCE fields (clu_id / fsn / tract / pp_acres), not John Deere operations, so
      // the label would be a no-op anyway. Routing a key through a display function is
      // how a grouping silently becomes a sentence the day the source emits a placeholder.
      const crop = (r.crop || "—").toUpperCase();
      // CORN GRAIN AND CORN SILAGE ARE TWO INSURANCE LINES, so they are two rows here,
      // as they are on the map's summary and the field sheet. Grouped on crop alone,
      // a dairy's silage and grain summed into one "Corn" total the grower could not
      // match against either line of their schedule. Untyped corn keeps its own row.
      const tc = crop === "CORN" ? String(r.type_code == null ? "" : r.type_code).trim() : "";
      const gk = crop + "|" + tc;
      const g = m.get(gk) || { key: gk, crop, tc, acres: 0, pp: 0, fields: [] };
      const ac = Number(acreOf ? acreOf(r) : r.acres) || 0;
      g.acres += ac;
      g.pp    += Number(r.pp_acres) || 0;
      g.fields.push({
        key: `${r.clu_id || ""}|${r.fsn}|${r.tract}|${r.field_id}|${gk}`,
        name: nameFor(r), county: cty ? cty(r) : r.county, acres: ac,
        pp: Number(r.pp_acres) || 0,
        yield: cluYield ? cluYield[String(r.clu_id || "").toLowerCase()] : null,
      });
      m.set(gk, g);
    }
    for (const g of m.values()) g.fields.sort((a, b) => b.acres - a.acres);
    return [...m.values()].sort((a, b) => b.acres - a.acres);
  }, [rows, labels, cluYield]);   // eslint-disable-line

  const total = byCrop.reduce((s, g) => s + g.acres, 0);
  const totalPP = byCrop.reduce((s, g) => s + g.pp, 0);
  const fieldCount = byCrop.reduce((s, g) => s + g.fields.length, 0);
  const maxCrop = byCrop.length ? byCrop[0].acres : 0;

  // ⚠ THIS PANEL IS THE ONE A GROWER READS, and it labelled this total "Insured
  // acres" while `acreOf` had already substituted the CLU BOUNDARY's own measured
  // acreage on every field the AIP reported none for. On OAKFIELD ALABAMA FARMS
  // (61 of 61 fields) and LAKESHORE DAIRY LLC (36 of 36) that is the WHOLE figure,
  // so the screen whose entire purpose is "are these the right acres?" was showing
  // a boundary measurement under the word insured.
  //
  // The split walks the SAME ladder acreOf walks, so it cannot disagree with the
  // total: a row whose own `acres` parses is reported; otherwise whatever acreOf
  // returned came from the boundary, or is the third state — no acreage at all,
  // which is not a zero-acre field.
  //
  // ⚠ AND A PRF FIELD HAS NO AIP ACREAGE TO BE MISSING. PRF insures a percentage of
  // value on a 0.25° rainfall grid; it does not insure acres on a field, so the AIP
  // never reports per-field acreage for a PRF policy and a NULL `acres` there is
  // CORRECT rather than absent. Measured CY2026: of 6,018 boundary-derived acres,
  // 5,628 (94%) are PRF rows, and four of the five farms with no AIP acre at all are
  // PRF-only — OAKFIELD ALABAMA, LAKESHORE DAIRY, SUNRISE AG, ROOTED ACRES. Only 390
  // acres across 9 farms are crop rows where a reported figure genuinely is missing.
  //
  // So the split is kept PER ROW, not per farm: VERRATTI FARMS carries both, and a
  // farm-level flag would either excuse its 75 missing crop acres or wrongly accuse
  // its 95 PRF ones. Without this, a PRF grower reads "AIP-reported acres 0 — none
  // reported on any field" and takes a correct screen for a broken one.
  const prov = useMemo(() => {
    let reported = 0, boundary = 0, nBoundary = 0, noAcre = 0, prfBoundary = 0, cropBoundary = 0;
    for (const r of (rows || [])) {
      const a = parseFloat(r.acres);
      if (Number.isFinite(a)) { reported += a; continue; }
      const ac = Number(acreOf ? acreOf(r) : 0) || 0;
      if (ac > 0) {
        boundary += ac; nBoundary++;
        if (String(r.crop || "").trim().toUpperCase() === "PRF") prfBoundary += ac; else cropBoundary += ac;
      } else noAcre++;
    }
    // "PRF explains all of it" is the only case that earns the softer sentence.
    const allPrf = boundary > 0 && cropBoundary === 0;
    return { reported, boundary, nBoundary, noAcre, prfBoundary, cropBoundary, allPrf };
  }, [rows, acreOf]);   // eslint-disable-line

  // The two sub-labels, named rather than inlined. Three reasons, and the third is the
  // one that forced it: the PRF wording is a RULE and belongs somewhere a reader can
  // find it; a ternary inside a JSX prop cannot be read aloud; and check-acre-provenance
  // 6g pins `label="Mapped acres"` within 200 characters of the schedule-of-insurance
  // tile, so any expression long enough to explain itself pushes them apart and fails
  // an assertion about something else entirely.
  //
  // ⚠ THIS PANEL IS ONLY EVER A GROWER'S, so it speaks the grower's words. It said
  // "AIP-reported acres" and "incl. CLU boundary measurements" (Kevin, 2026-10-31
  // sweep): AIP and CLU are our file names, not anything on a farmer's paperwork.
  // Same two quantities, named for what the farmer holds: the schedule of insurance
  // in the mail, and the field maps on this screen.
  const mappedSub = prov.allPrf ? "measured from your field boundaries" : "includes acres measured from your field maps";
  const reportedSub = prov.reported > 0 ? "from your schedule of insurance"
    : prov.allPrf ? "PRF pays on rainfall, not acres — none to report"
    : "none reported on any field";

  if (!byCrop.length) {
    return (
      <div style={{ ...uiCard(24), textAlign: "center", color: T.muted, fontSize: 14, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 800, color: T.ink, marginBottom: 6 }}>No fields on file yet.</div>
        Your acres appear here once your acreage report is processed — usually after the
        mid-summer reporting deadline.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ marginBottom: 14 }}>
        <h3 style={{ margin: 0, color: T.ink, fontSize: isMobile ? 19 : 22, fontWeight: 800, fontFamily: T.serif }}>
          What we have you insured for
        </h3>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 3 }}>
          Check the acres against your records. Tell us about anything that looks wrong.
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {/* Where every field carries the AIP's own reported acreage the figure IS
            the insured one and keeps that name. Where any of it is a boundary
            measurement the total is renamed, and the schedule-of-insurance half is shown
            BESIDE it so the grower can see which number came from their schedule
            of insurance — on the two farms where that half is 0.0, seeing it is
            the whole point. */}
        {/* ⚠ "none reported on any field" IS TRUE AND READS AS A FAULT ON A PRF FARM.
            PRF pays on a rainfall index, so there is no per-field acreage for the carrier
            to have reported — the zero is the product, not a gap. Only say that where PRF
            accounts for ALL of the unreported acreage; a farm with crop acres missing too
            still gets the plain sentence, because there the zero really is something to
            ask about. (This note sits ABOVE the branch rather than between the two tiles
            because check-acre-provenance 6g pins them within 200 characters of each
            other — see the note added beside that assertion.) */}
        {prov.boundary > 0 ? (
          <>
            <StatTile label="Mapped acres" value={fmtAc ? fmtAc(total) : Math.round(total)}
              sub={mappedSub} isMobile={isMobile} />
            <StatTile label="Acres on your schedule of insurance" value={fmtAc ? fmtAc(prov.reported) : Math.round(prov.reported)}
              sub={reportedSub} isMobile={isMobile} />
          </>
        ) : (
          <StatTile label="Insured acres" value={fmtAc ? fmtAc(total) : Math.round(total)} isMobile={isMobile} />
        )}
        <StatTile label="Fields" value={String(fieldCount)} isMobile={isMobile} />
        <StatTile label="Crops" value={String(byCrop.length)} isMobile={isMobile} />
        {totalPP > 0 && <StatTile label="Prevent plant" value={fmtAc ? fmtAc(totalPP) : Math.round(totalPP)} sub="acres not planted" isMobile={isMobile} />}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {byCrop.map(g => {
          const open = openCrop === g.key;
          const meta = SECTION_META[FALL_CROPS.includes(g.crop) ? "fall" : "spring"] || {};
          return (
            <div key={g.key} style={uiCard(isMobile ? 13 : 15)}>
              <div onClick={() => setOpenCrop(open ? null : g.key)}
                style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, color: T.faint, width: 12 }}>{open ? "▼" : "▶"}</span>
                <div style={{ flex: "1 1 150px", minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: T.ink, fontSize: isMobile ? 14.5 : 16 }}>{fmCropName(g.crop, g.tc)}</div>
                  <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>
                    {g.fields.length} field{g.fields.length === 1 ? "" : "s"}
                    {g.pp > 0 && <span style={{ color: T.warn, fontWeight: 700 }}> · {fmtAc(g.pp)} ac prevent plant</span>}
                  </div>
                </div>
                {/* A bar makes a wrong acre figure LOOK wrong at a glance — the whole
                    point of this screen over a mailed schedule. */}
                <div style={{ flex: "1 1 160px", minWidth: 110, height: 10, background: T.surfaceAlt,
                              borderRadius: 5, overflow: "hidden" }}>
                  <div style={{ width: `${maxCrop > 0 ? (g.acres / maxCrop) * 100 : 0}%`, height: "100%",
                                background: meta.border || T.green, borderRadius: 5 }} />
                </div>
                <div style={{ ...uiNum, fontSize: isMobile ? 17 : 20, fontWeight: 800, color: T.ink, minWidth: 92, textAlign: "right" }}>
                  {fmtAc(g.acres)} <span style={{ fontSize: 11.5, fontWeight: 600, color: T.muted }}>ac</span>
                </div>
              </div>

              {open && (
                <div style={{ marginTop: 12, borderTop: `1px solid ${T.lineSoft}`, paddingTop: 8 }}>
                  {g.fields.map(f => (
                    <div key={f.key} style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "6px 0",
                                              borderBottom: `1px solid ${T.lineSoft}`, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, color: T.ink, fontSize: 13, flex: "1 1 150px" }}>{f.name}</span>
                      <span style={{ fontSize: 11.5, color: T.muted, minWidth: 84 }}>{titleCaseCounty(f.county || "")}</span>
                      {f.yield && f.yield.value > 0 && (
                        <span style={{ ...uiNum, fontSize: 11.5, color: "#1f5480", fontWeight: 700, minWidth: 96 }}
                          title="From your combine, via John Deere Operations Center">
                          {jdYieldLabel(f.yield.crop, f.yield.value, f.yield.unit)}
                        </span>
                      )}
                      {f.pp > 0 && (
                        <span style={{ fontSize: 10.5, fontWeight: 800, color: T.warn, background: "#f7efdc",
                                       borderRadius: 4, padding: "1px 7px" }}>
                          {fmtAc(f.pp)} ac PP
                        </span>
                      )}
                      <span style={{ ...uiNum, fontSize: 13.5, fontWeight: 700, color: T.ink, minWidth: 74, textAlign: "right" }}>
                        {fmtAc(f.acres)} ac
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ ...uiCard(isMobile ? 13 : 15), marginTop: 14, fontSize: 13, color: T.body, lineHeight: 1.6 }}>
        <b style={{ color: T.ink }}>See something that isn’t right?</b>{" "}
        Acres, a missing field, or a crop that changed after you reported — let us know and we’ll
        correct it. <a href="tel:+15852331066" style={{ color: T.green, fontWeight: 700, whiteSpace: "nowrap" }}>(585) 233-1066</a> ·{" "}
        <a href="mailto:office@cloverag.com" style={{ color: T.green, fontWeight: 700 }}>office@cloverag.com</a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FIELD INSPECTOR — the third pane. Crop-insurance facts first, Deere second.
//
// Scope follows the selection, so one panel answers three questions without
// changing screens:
//   • nothing selected → the whole book, by crop and by county
//   • farms selected   → those operations rolled up, by crop
//   • a field clicked  → that field: its coverage, and what the combine did on it
//
// This is the piece Field Mapper never had. Insurance data existed only as a
// hover tooltip and Deere data only as a modal, so "how is this operation
// covered, and how did it actually yield" was never answerable on one screen.
// ─────────────────────────────────────────────────────────────────────────────
function FieldInspector({ scope, rows = [], guar = null, cluYield = null, farms = [], selSet, jdSel, isMobile, jdDataSet = null, onOpenJdReports = null }) {
  // "Yield reports" opens JdFieldDataView — the By field / By area / Machine vs FSA
  // / Harvest Watch report set, with Draft AR. It was previously reachable ONLY via
  // the sticky-farm chip, which only appears when you deep-link in from a farmer
  // card, so from Field Mapper itself these reports were unreachable.
  const jdReportBtn = (fid, name) => {
    if (!onOpenJdReports || !fid) return null;
    if (jdDataSet && !jdDataSet.has(fid)) return null;   // no machine data for this farm
    return (
      <button onClick={() => onOpenJdReports(fid, name, null, null, null, "Field Mapper")}
        title="John Deere reports — by field, by area, machine vs FSA, harvest watch, and Draft AR"
        style={{ marginTop: 12, width: "100%", border: `1px solid #bcdcb4`, borderRadius: T.r.sm,
                 padding: "8px", cursor: "pointer", fontWeight: 800, fontSize: 12.5,
                 background: "#eaf3e6", color: T.ink, fontFamily: "inherit" }}>
        Yield reports
      </button>
    );
  };

  const money0 = (n) => (n > 0 ? fmtD0(n) : "—");
  const ac1 = (n) => (n > 0 ? `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} ac` : "—");

  const H = { ...uiLabel, fontSize: 10.5, marginBottom: 6 };
  const Row = ({ k, v, strong }) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0", fontSize: 12.5 }}>
      <span style={{ color: T.muted }}>{k}</span>
      <span style={{ ...uiNum, color: strong ? T.ink : T.body, fontWeight: strong ? 800 : 600, textAlign: "right" }}>{v}</span>
    </div>
  );
  const Section = ({ title, children }) => (
    <div style={{ borderTop: `1px solid ${T.lineSoft}`, paddingTop: 10, marginTop: 10 }}>
      <div style={H}>{title}</div>
      {children}
    </div>
  );

  // ── Field level ────────────────────────────────────────────────────────────
  if (scope === "field" && jdSel) {
    const p = jdSel;
    const y = cluYield ? cluYield[String(p.clu_id || "").toLowerCase()] : null;
    // Every insured line on this CLU. A split-planted field carries more than one.
    const lines = (rows || []).filter(r => String(r.clu_id || "").toLowerCase() === String(p.clu_id || "").toLowerCase());
    return (
      <>
        <div style={{ fontWeight: 800, color: T.ink, fontSize: 14.5, lineHeight: 1.3 }}>
          {p.label || `${p.fsn || "—"}-${p.tract || "—"}-${p.field || "—"}`}
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>
          {p.farmer || ""}{p.county ? ` · ${titleCaseCounty(p.county)}` : ""}
        </div>
        <div style={{ ...uiNum, fontSize: 20, fontWeight: 800, color: T.ink, marginTop: 8 }}>
          {ac1(Number(p.fieldAc || p.acres || 0))}
        </div>

        <Section title="Insurance">
          {lines.length ? lines.map((r, i) => (
            <div key={i} style={{ marginBottom: i < lines.length - 1 ? 8 : 0 }}>
              <Row k={jdCropLabel(r.crop)} v={r.acres != null ? ac1(Number(r.acres)) : "—"} strong />
              {r.policy_number && <Row k="Policy" v={r.policy_number} />}
            </div>
          )) : <div style={{ fontSize: 12, color: T.faint }}>No insured line on this field.</div>}
          {p.pp && <Row k={p.ppFull ? "Prevent plant" : "Partial PP"} v={ac1(Number(p.ppAc || 0))} strong />}
        </Section>

        <Section title="John Deere">
          {y ? (
            <>
              <Row k={jdHarvestWord(y.unit) === "chopper" ? "Chopper yield" : "Combine yield"} v={jdYieldLabel(y.crop, y.value, y.unit)} strong />
              {y.dm != null && <Row k="Dry matter" v={`${Number(y.dm).toFixed(1)} DM ton/ac`} />}
              <Row k="Crop" v={jdCropLabel(y.crop)} />
              <Row k="Harvested" v={y.date ? String(y.date).slice(0, 10) : "—"} />
              <div style={{ fontSize: 11, color: T.faint, marginTop: 6, lineHeight: 1.5 }}>
                Click the field on the map for the in-field yield map.
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: T.faint }}>No machine data for this field.</div>
          )}
          {jdReportBtn(p.farmer_id, p.farmer)}
        </Section>
      </>
    );
  }

  // ── Operation level (one or more farms selected) ────────────────────────────
  if (scope === "operation") {
    const ids = [...(selSet || [])];
    const picked = (farms || []).filter(f => ids.includes(f.id));
    const byCrop = {};
    let liabTot = 0;
    for (const id of ids) {
      const g = guar && guar[id];
      if (!g) continue;
      for (const crop in g.byCrop) {
        const s = g.byCrop[crop];
        const t = byCrop[crop] || (byCrop[crop] = { acres: 0, liab: 0, bu: 0, aphW: 0, w: 0, plans: new Set() });
        t.acres += s.acres || 0; t.liab += s.liab || 0; t.bu += s.bu || 0;
        if (s.aph != null && s.acres > 0) { t.aphW += s.aph * s.acres; t.w += s.acres; }
        if (s.plan) t.plan = s.plan;
        liabTot += s.liab || 0;
      }
    }
    const crops = Object.entries(byCrop).sort((a, b) => b[1].liab - a[1].liab);
    const acTot = crops.reduce((s, [, v]) => s + v.acres, 0);
    return (
      <>
        <div style={{ fontWeight: 800, color: T.ink, fontSize: 14.5, lineHeight: 1.3 }}>
          {picked.length === 1 ? picked[0].name : `${picked.length} operations`}
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>
          {/* groups[].fields is an ARRAY of field rows, not a count. Adding a number
              to it coerced the array to a string and printed
              "0[object Object],[object Object]..." across the panel. */}
          {(() => {
            const n = picked.reduce((s, f) => s + (Array.isArray(f.fields) ? f.fields.length : (Number(f.fields) || 0)), 0);
            return n > 0 ? `${n.toLocaleString()} field${n === 1 ? "" : "s"}` : "";
          })()}
        </div>
        <div style={{ ...uiNum, fontSize: 20, fontWeight: 800, color: T.ink, marginTop: 8 }}>{money0(liabTot)}</div>
        <div style={{ fontSize: 11, color: T.faint }}>revenue protected · {ac1(acTot)}</div>

        {crops.length ? crops.map(([crop, v]) => (
          <Section key={crop} title={crop}>
            <Row k="Insured acres" v={ac1(v.acres)} strong />
            {v.plan && <Row k="Plan" v={v.plan} />}
            {v.w > 0 && <Row k="APH (acre-wtd)" v={(v.aphW / v.w).toFixed(1)} />}
            <Row k="Liability" v={money0(v.liab)} />
            {v.acres > 0 && <Row k="Guarantee" v={`${fmtD0(v.liab / v.acres)}/ac`} />}
            {v.bu > 0 && <Row k="Bushel guarantee" v={`${Math.round(v.bu).toLocaleString()} bu`} />}
          </Section>
        )) : (
          <Section title="Insurance">
            <div style={{ fontSize: 12, color: T.faint }}>No coverage loaded for this selection.</div>
          </Section>
        )}
        {/* One operation selected → its report set. With several selected there's no
            single farm to report on, so the button is withheld rather than guessing. */}
        {picked.length === 1 && jdReportBtn(picked[0].id, picked[0].name)}
      </>
    );
  }

  // Nothing selected → nothing to inspect. The book-wide rollup that used to live
  // here was removed 2026-07-30: the Dashboard already answers book-level
  // questions, and the stat strip above the map carries fields/acres/farmers/
  // counties/crops. Returning null lets the caller drop the whole column, so the
  // default state of this screen is map.
  return null;
}

// Searchable farm-selector dropdown for the Field Mapper header — JD-org-switcher
// style. Shows the current selection, opens a filterable list, picks ONE farm (or
// All). A green dot marks farms with John Deere data so you can jump straight to them.
function FarmPicker({ groups, selectedId = null, selectedName = null, jdDataSet, onPick, onClear, big = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  // Name the selected farm from the caller when the active filters have taken it out
  // of `groups` — a picker that says "1 farm" cannot tell you WHICH, and a crop or
  // county filter narrowing the list is normal, not an error state.
  const selId = selectedId || null;
  const label = selId
    ? ((groups.find(g => g.id === selId) || {}).name || selectedName || "1 farm")
    : "All farms";
  const ql = q.trim().toLowerCase();
  const list = [...groups].filter(g => g && g.name && (!ql || String(g.name).toLowerCase().includes(ql)))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const jd = (id) => jdDataSet && jdDataSet.has(id);
  const item = (active) => ({ display: "flex", alignItems: "center", gap: 7, padding: big ? "12px 12px" : "7px 10px", cursor: "pointer",
    fontSize: big ? 14.5 : 12.5, fontWeight: active ? 800 : 600, color: "#2d3f2d", background: active ? "#eaf3e6" : "#fff", borderBottom: "1px solid #f0f4f0" });
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} title="Pick a farm"
        style={big
          ? { display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", fontFamily: "inherit",
              border: "none", borderRadius: 999, height: 40, padding: "0 15px", fontSize: 13.5, fontWeight: 800,
              maxWidth: "calc(100vw - 40px)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              background: "#fff", color: "#1a2e1a", boxShadow: "0 6px 18px rgba(0,0,0,.28)" }
          : { display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit",
          border: "1px solid #cdd9cd", borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 700,
          maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          background: selId ? "#eef6ec" : "#fff", color: "#2d4a2d" }}>
        {label} <span style={{ fontSize: 10 }}>▼</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 1200, width: 288, maxWidth: "80vw",
          background: "#fff", border: "1px solid #cdd9cd", borderRadius: 10, boxShadow: "0 10px 34px rgba(0,0,0,0.22)", overflow: "hidden" }}>
          <div style={{ padding: 8, borderBottom: "1px solid #eef2ee" }}>
            <input autoFocus={!big} value={q} onChange={e => setQ(e.target.value)} placeholder="Search farms…"
              style={{ width: "100%", boxSizing: "border-box", fontSize: big ? 16 : 12.5, padding: big ? "9px 12px" : "7px 10px", borderRadius: 7, border: "1px solid #cdd9cd" }} />
          </div>
          <div style={{ maxHeight: big ? "52vh" : 320, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
            <div onClick={() => { onClear(); setOpen(false); setQ(""); }} style={item(!selId)}>All farms</div>
            {list.map(g => (
              <div key={g.id} onClick={() => { onPick(g.id); setOpen(false); setQ(""); }} style={item(g.id === selId)}>
                {/* A plain green dot, not a tractor emoji: same signal, the app's own look. */}
                <span aria-label={jd(g.id) ? "Has John Deere data" : undefined} title={jd(g.id) ? "Has John Deere data" : undefined}
                  style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: jd(g.id) ? T.green : "transparent" }} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</span>
              </div>
            ))}
            {!list.length && <div style={{ padding: "10px 12px", fontSize: 12, color: "#677167" }}>No farms match "{q}"</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── FIELD MAPPER ON A PHONE (Kevin, 2026-10-31: "make it awesome") ─────────────
// The phone layout is its own shape, not the desktop squeezed: the map takes the
// whole screen, search and the year float on it, and everything else lives in ONE
// bottom sheet that moves between a farm summary, the field list and a field's
// page. These helpers are shared by the sheet's list rows and its field page.
// Names a grower uses, for the John Deere codes that don't say it plainly.
const FM_CROP_NAMES = { RETTICH: "Radish", RAPE_SEED: "Rapeseed", PEA_TRAPPER: "Trapper peas",
  TALL_FESCUE: "Tall fescue", FORAGE_MIX: "Forage mix", SUNFLOWER_OIL: "Oil sunflowers",
  GREEN_BEANS: "Green beans", LIMA_BEANS: "Lima beans", GRASS_FORAGE: "Grass forage",
  GRASS_ALFALFA: "Grass and alfalfa", NONE: "No crop", PRF: "PRF",
  // Sentence case like "Corn grain", and a name a grower would say for the code John
  // Deere writes when the operator never picked a crop (Lawnel 2: 21 hay-cut maps).
  "CORN SILAGE": "Corn silage", "SORGHUM SILAGE": "Sorghum silage", UNSPECIFIED_CROP: "Unknown crop" };
const fmTitleCase = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const fmCropName = (crop, tc) => {
  const c = String(crop || "").toUpperCase();
  if (c === "CORN") return tc === "026" ? "Corn silage" : tc === "016" ? "Corn grain" : "Corn";
  if (FM_CROP_NAMES[c]) return FM_CROP_NAMES[c];
  // John Deere's own codes carry underscores (TALL_FESCUE, PEA_TRAPPER); a chip that
  // reads "Rape_seed" is the old all-caps code with a capital letter on it.
  return fmTitleCase(String(crop || "").replace(/_/g, " ")) || "—";
};
// 1900-01-01 is the AIP's "no date" sentinel, so anything before 2000 prints as no date.
const fmMd = (d) => (d && /^\d{4}-\d{2}-\d{2}/.test(String(d)) && Number(String(d).slice(0, 4)) >= 2000
  ? `${Number(String(d).slice(5, 7))}/${String(d).slice(8, 10)}` : null);
// A field's outline as an SVG path, longitude squeezed by cos(latitude) so a square
// field is drawn square. Same drawing the field card has always used.
function fmOutlinePath(geom, size) {
  if (!geom || !geom.coordinates) return null;
  const rings = geom.type === "Polygon" ? [geom.coordinates[0]] : geom.type === "MultiPolygon" ? geom.coordinates.map((pg) => pg[0]) : [];
  const pts = rings.flat().filter((c) => Array.isArray(c) && c.length >= 2);
  if (pts.length < 3) return null;
  const lat0 = pts.reduce((a, c) => a + c[1], 0) / pts.length;
  const kx = Math.cos((lat0 * Math.PI) / 180);
  const xs = pts.map((c) => c[0] * kx), ys = pts.map((c) => -c[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const span = Math.max(maxX - minX, maxY - minY, 1e-9);
  const inner = size - 4, sc = inner / span;
  const ox = 2 + (inner - (maxX - minX) * sc) / 2, oy = 2 + (inner - (maxY - minY) * sc) / 2;
  return rings.map((r) => r.map((c, k) => (k ? "L" : "M") + ((c[0] * kx - minX) * sc + ox).toFixed(1) + " " + ((-c[1] - minY) * sc + oy).toFixed(1)).join(" ") + " Z").join(" ");
}
function FmOutline({ geom, size = 40, color = "#9aa89a", dashed = false }) {
  const d = fmOutlinePath(geom, size);
  if (!d) {
    return (
      <div style={{ width: size, height: size, borderRadius: 8, border: "1.5px dashed #b7c4b6", boxSizing: "border-box", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5, fontWeight: 800, color: "#5f6f5e" }}>
        {geom && geom.type === "Point" ? "pin" : "none"}
      </div>
    );
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d={d} fill={color} fillOpacity={dashed ? 0.15 : 0.35} stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeDasharray={dashed ? "4 3" : undefined} />
    </svg>
  );
}
// Is (lng, lat) inside this GeoJSON polygon? Ray casting over the outer ring, minus holes.
function fmRingHas(ring, x, y) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
    if (((yi > y) !== (yj > y)) && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi) inside = !inside;
  }
  return inside;
}
function fmGeomContains(geom, lng, lat) {
  if (!geom || !geom.coordinates) return false;
  const polys = geom.type === "Polygon" ? [geom.coordinates] : geom.type === "MultiPolygon" ? geom.coordinates : [];
  return polys.some((pg) => pg && pg[0] && fmRingHas(pg[0], lng, lat) && !pg.slice(1).some((h) => fmRingHas(h, lng, lat)));
}
const fmTint = (hex) => (/^#[0-9a-f]{6}$/i.test(String(hex || "")) ? hex + "2e" : "#eef3ee");
// "Show on map", drawn rather than an emoji pin (no emoji in the app's own UI).
const FmPinIcon = ({ size = 13, color = "#2d6a1a" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: "-2px" }}>
    <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

// Rain and rotation for ONE field, on its phone page. Both read tables this screen
// already reads in full on the Weather and History tabs (field_precip_daily, per CLU;
// field_crop_history), just for the one field instead of the farm.
function FmPhoneFieldExtras({ cluId, farmerId, yRow, curYear = null, curCrop = null, curTc = null, prf = false }) {
  const clu = cluLower(cluId);
  const [rain, setRain] = useState(null);   // null loading · "none" · { d7, d30, thru }
  // A PRF field is pasture: its number is the grid's rainfall index, not a combine
  // reading (Kevin, 2026-10-31: "This is a PRF field; it should show PRF data").
  // null loading · "none" · { code, index, isFinal, grid }. Index only, never dollars.
  const [prfIdx, setPrfIdx] = useState(null);
  useEffect(() => {
    if (!prf || !clu) { setPrfIdx("none"); return; }
    let alive = true; setPrfIdx(null);
    const yr = curYear || CURRENT_CROP_YEAR;
    (async () => {
      try {
        // ⚠ THE YEAR ON SCREEN ONLY. Unfiltered, a CLU with lines in two crop years
        // took its grid from whichever row came back first and counted the other
        // year's intervals as "yours" — the same field, two years, mixed on one tile.
        const fpi = (await sbFetch("field_prf_intervals", { select: "grid_id,interval_code,crop_year", filters: [`clu_id=ilike.${clu}`, `crop_year=eq.${yr}`] })) || [];   // ilike: 65% of stored CLUs are upper case (checked 2026-11-03)
        const grid = fpi.length ? fpi[0].grid_id : null;
        if (grid == null) { if (alive) setPrfIdx("none"); return; }
        const mine = new Set(fpi.filter((r) => String(r.grid_id) === String(grid)).map((r) => r.interval_code));
        const ix = (await sbFetch("prf_grid_indices", { select: "interval_code,estimated_index_value,final_index_value,is_final,estimated_rainfall_inches,normal_rainfall_inches,normal_to_date_inches,estimated_as_of",
          filters: [`grid_id=eq.${grid}`, `crop_year=eq.${yr}`] })) || [];
        const by = {};
        // One rule for every PRF screen (src/prf/prfIntervalStatus.js): an open
        // interval has no index, only percent of normal to date.
        for (const r of ix) {
          const rd = PRF_IS.intervalReading(r.interval_code, yr, {
            finalValue: r.is_final ? r.final_index_value : null, estimatedValue: r.estimated_index_value,
            estimatedInches: r.estimated_rainfall_inches, normalInches: r.normal_rainfall_inches,
            normalToDateInches: r.normal_to_date_inches, estimatedAsOf: r.estimated_as_of });
          if (rd.index != null && isFinite(rd.index)) by[r.interval_code] = { index: rd.index, toDate: null, isFinal: rd.source === "FINAL" };
          else if (rd.toDate != null && isFinite(rd.toDate)) by[r.interval_code] = { index: null, toDate: rd.toDate, isFinal: false };
        }
        // ⚠ WHICH INTERVAL THE TILE SPEAKS FOR. It took the newest interval with any
        // reading, so a just-opened interval's first-week estimate ("38% of normal", in
        // red) stood for the field with none of the timing check the PRF page applies
        // before it alarms, and with no insured reading it fell back to a grid interval
        // the grower never bought without saying so. Now: the newest of THIS FIELD'S
        // intervals that is final or at least half over (the PRF page's own gate); else
        // its newest one, labelled an early estimate; only then the grid's, named as not
        // theirs.
        const order = WXP.ORDER.slice().reverse();   // the eleven RMA intervals; no DECJAN
        const settled = (c) => by[c] && by[c].index != null;
        const code = order.find((c) => mine.has(c) && settled(c)) || order.find((c) => mine.has(c) && by[c])
                  || order.find((c) => settled(c)) || order.find((c) => by[c]) || null;
        if (alive) setPrfIdx(code ? { code, ...by[code], grid, insured: mine.has(code), early: !settled(code) } : "none");
      } catch (e) { if (alive) setPrfIdx("none"); }
    })();
    return () => { alive = false; };
  }, [clu, prf, curYear]);
  const [hist, setHist] = useState(null);   // null loading · [{ year, crop, tc, pp }]
  useEffect(() => {
    if (!clu) { setRain("none"); return; }
    let alive = true; setRain(null);
    const from = new Date(Date.now() - 40 * 86400000).toISOString().slice(0, 10);
    sbFetch("field_precip_daily", { select: "obs_date,precip_mm", filters: [`clu_id=eq.${clu}`, `obs_date=gte.${from}`] })
      .then((rs) => {
        if (!alive) return;
        const list = (rs || []).filter((r) => r && r.obs_date);
        if (!list.length) { setRain("none"); return; }
        // ⚠ THE SAME WINDOW AS THE WEATHER PAGE: the last N days ENDING YESTERDAY (the last
        // full day). This counted back from the newest day on file instead, so one field
        // showed two different "7 days" totals a tap apart. A feed that runs behind no
        // longer moves the window; it shows as "partial", the Weather page's own word,
        // when fewer than 80% of the window's days are on file.
        const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        const tY = Date.parse(yest + "T00:00:00Z");
        const inWin = (r, days) => { const dt = tY - Date.parse(String(r.obs_date).slice(0, 10) + "T00:00:00Z"); return dt >= 0 && dt < days * 86400000; };
        const win = (days) => {
          const rs2 = list.filter((r) => inWin(r, days));
          return { inches: rs2.reduce((a, r) => a + (Number(r.precip_mm) || 0), 0) / 25.4,
                   present: new Set(rs2.map((r) => String(r.obs_date).slice(0, 10))).size, days };
        };
        const w7 = win(7), w30 = win(30);
        setRain({ d7: w7.inches, d30: w30.inches, p7: w7.present, p30: w30.present, thru: yest });
      })
      .catch(() => { if (alive) setRain("none"); });
    return () => { alive = false; };
  }, [clu]);
  useEffect(() => {
    if (!clu) { setHist([]); return; }
    let alive = true; setHist(null);
    sbFetch("field_crop_history", { select: "crop_year,crop,acres,pp_acres", filters: [`clu_id=ilike.${clu}`, ...(farmerId ? [`farmer_id=eq.${farmerId}`] : [])] })
      .then((rs) => {
        if (!alive) return;
        const by = {};
        for (const h of rs || []) {
          const y = Number(h.crop_year); if (!y || !h.crop) continue;
          // one crop per year: the larger-acre crop, as the History tab does
          if (!by[y] || (Number(h.acres) || 0) > (Number(by[y].acres) || 0)) by[y] = { year: y, crop: h.crop, acres: h.acres, pp: (Number(h.pp_acres) || 0) > 0 };
        }
        setHist(Object.values(by));
      })
      .catch(() => { if (alive) setHist([]); });
    return () => { alive = false; };
  }, [clu, farmerId]);

  // The year on screen comes from the live field record, so it wins over the history table.
  const years = React.useMemo(() => {
    const by = {};
    for (const h of hist || []) by[h.year] = h;
    if (curYear && curCrop) by[curYear] = { ...(by[curYear] || {}), year: curYear, crop: curCrop, tc: curTc };
    return Object.values(by).sort((a, b) => a.year - b.year).slice(-5);
  }, [hist, curYear, curCrop, curTc]);

  const tile = { border: "1px solid #dde7dd", borderRadius: 12, padding: "10px 12px", minWidth: 0 };
  const tLbl = { fontSize: 10.5, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "#5f6f5e" };
  const hasY = !!(yRow && yRow.value > 0);
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 8, marginTop: 10 }}>
        <div style={tile}>
          <div style={tLbl}>Rain, last 7 days</div>
          {rain === null ? <div style={{ fontSize: 13, color: "#5f6f5e", marginTop: 4 }}>Loading…</div>
            : rain === "none" ? <div style={{ fontSize: 13, color: "#5f6f5e", marginTop: 4 }}>No rain data for this field yet</div>
            : <>
                <div style={{ ...uiNum, fontSize: 20, fontWeight: 800, color: "#1f5fb0" }}>{rain.d7.toFixed(2)}"</div>
                <div style={{ fontSize: 11.5, color: "#4f6a4c" }}>through {fmMd(rain.thru)}{rain.p7 < Math.ceil(7 * 0.8) ? ` · partial, ${rain.p7} of 7 days` : ""}</div>
              </>}
        </div>
        {prf && !hasY ? (
          <div style={tile}>
            <div style={tLbl}>PRF index{prfIdx && prfIdx !== "none" ? " · " + (PRF_INTERVAL_LABELS[prfIdx.code] || prfIdx.code) : ""}</div>
            {prfIdx === null ? <div style={{ fontSize: 13, color: "#5f6f5e", marginTop: 4 }}>Loading…</div>
              : prfIdx === "none" ? <div style={{ fontSize: 13, color: "#5f6f5e", marginTop: 4 }}>No index for this grid yet</div>
              : <>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    {/* An open interval gets no red/green: it is read to date, never judged. */}
                    <span style={{ width: 10, height: 10, borderRadius: 5, background: prfIdx.index == null ? "#b8c2b8" : prfIndexColor(prfIdx.index), flexShrink: 0 }} />
                    <span style={{ ...uiNum, fontSize: 20, fontWeight: 800, color: "#1a2e1a" }}>{Math.round(prfIdx.index != null ? prfIdx.index : prfIdx.toDate)}% of normal{prfIdx.index == null ? " so far" : ""}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#4f6a4c" }}>
                    Grid {prfIdx.grid} · {prfIdx.isFinal ? "final" : prfIdx.index == null ? "interval open" : "estimate at close"}
                    {!prfIdx.insured ? " · not one of your intervals" : ""}
                  </div>
                </>}
          </div>
        ) : hasY ? (
          <div style={tile}>
            <div style={tLbl}>{jdHarvestWord(yRow.unit) === "chopper" ? "Chopper yield" : "Combine yield"}</div>
            <div style={{ ...uiNum, fontSize: 20, fontWeight: 800, color: "#1a2e1a" }}>{jdYieldLabel(yRow.crop, yRow.value, yRow.unit)}</div>
            {yRow.dm != null && <div style={{ fontSize: 11.5, color: "#4f6a4c" }}>DM {Number(yRow.dm).toFixed(1)} ton/ac</div>}
            <div style={{ fontSize: 11.5, color: "#4f6a4c" }}>{yRow.date ? String(yRow.date).slice(0, 4) + " harvest · " : ""}John Deere</div>
          </div>
        ) : (
          <div style={tile}>
            <div style={tLbl}>Rain, last 30 days</div>
            {rain && rain !== "none"
              ? <><div style={{ ...uiNum, fontSize: 20, fontWeight: 800, color: "#1f5fb0" }}>{rain.d30.toFixed(2)}"</div>
                  <div style={{ fontSize: 11.5, color: "#4f6a4c" }}>through {fmMd(rain.thru)}{rain.p30 < Math.ceil(30 * 0.8) ? ` · partial, ${rain.p30} of 30 days` : ""}</div></>
              : <div style={{ fontSize: 13, color: "#5f6f5e", marginTop: 4 }}>{rain === null ? "Loading…" : "—"}</div>}
          </div>
        )}
      </div>
      {years.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ ...tLbl, marginBottom: 6 }}>Crop history</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${years.length}, minmax(0,1fr))`, gap: 5, textAlign: "center" }}>
            {years.map((h) => (
              <div key={h.year} style={{ borderRadius: 8, padding: "5px 2px", background: fmTint(fieldCropColor(h.crop, h.tc)),
                                         outline: h.year === curYear ? "2px solid #1a2e1a" : "none", outlineOffset: -2, minWidth: 0 }}>
                <div style={{ fontSize: 10.5, color: "#4f6a4c" }}>{h.year}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#1a2e1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {fmCropName(h.crop, h.tc).replace(/^Soybeans$/, "Soy")}{h.pp ? " · PP" : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function FieldMapPanel({ rows, groups, prices = {}, isMobile, mode = null, cluAcres = {}, labels = {}, jdNames = {}, cluYield = null, cluEvidence = null, onSaveLabel = null, onViewWeather = null, onViewPulse = null, onViewPRF = null, onSelectFarm = null, selectedFarmerId = null, focus = null, onFocusDone = null, onViewport = null, initialView = null, jdDataSet = null, onOpenJdReports = null, allClus = "", restrictFarmerIds = null,
                        chopMode = false, chopSel = null, onChopToggle = null, phone = null, cropYear = CURRENT_CROP_YEAR, prfAcres = null }) {
  const isPortal = mode === "portal";   // hide the shapefile export for portal (view-only)
  // Phone mode (see PHONE MODE below). Read through a ref inside the layer build so the
  // build does not re-run on every render of the caller's `phone` object.
  const phoneRef = useRef(null);
  phoneRef.current = phone;
  const openFeatRef = useRef(null);   // openFeat (declared further down), for the locate effect
  const selFieldRef = useRef(null);   // mirrors selField (declared further down) for the layer build
  const fitPadRef = useRef(null);   // { top, bottom } px the phone chrome covers
  const fitOpts = (pad, maxZoom) => {
    const fp = phoneRef.current ? (fitPadRef.current || { top: 120, bottom: 260 }) : null;
    return fp ? { paddingTopLeft: [Math.round(pad / 2), fp.top], paddingBottomRight: [Math.round(pad / 2), fp.bottom], maxZoom }
              : { padding: [pad, pad], maxZoom };
  };
  const mapEl = useRef(null), mapRef = useRef(null), layerRef = useRef(null), countyRef = useRef(null), measureRef = useRef(null);
  const fitKeyRef = useRef("");   // only re-fit bounds when the field SET changes, not on label/name edits
  const [L, setL] = useState(null);
  const [mapFail, setMapFail] = useState(false);   // Leaflet never loaded: say so on the phone sheet
  const [geoms, setGeoms] = useState(null);
  // THE SELECTED FARM IS OWNED ABOVE THIS COMPONENT — it is not state here.
  //
  // It used to be: a local `selSet` of farmer_ids, written by the rail's checkboxes
  // AND by a farm-picker chip, then synced UP to the screen's `selectedFarmerId` by
  // an effect that only fired at size 0 or 1. Two selection states that agreed most
  // of the time is worse than one that always does — check a second farm and the map
  // drew two while Acreage / Weather / History stayed scoped to the first, with the
  // "Viewing:" chip still naming one of them. Consolidated 2026-08-13: ONE selector
  // (the farm picker in the screen toolbar), ONE piece of state, and everything on
  // the map derives from it. Every downstream feature that reads a farm — Print maps,
  // Soil, the FSA report, the JD boundary layer — already required exactly one.
  //
  // Kept as a Set named `selSet` because ~25 call sites below ask it `.has()` /
  // `.size`, and those read correctly whether the answer comes from one farm or many.
  const selSet = useMemo(() => new Set(selectedFarmerId ? [selectedFarmerId] : []), [selectedFarmerId]);
  const [exporting, setExporting] = useState(false);
  // Print-map grouping tightness, in miles of ground across a page. Smaller = more
  // pages, bigger fields on each. 2 mi reads comfortably at letter landscape.
  const [printSpanMi, setPrintSpanMi] = useState(2);
  const [printKind, setPrintKind] = useState("maps");   // maps | fsa
  // A PORTAL USER PRINTS THE MAPS, NOT THE ACREAGE REPORT. Both documents hang off
  // one control, and they are not the same kind of thing: the field maps are a
  // picture of the grower's own ground, while the FSA planting report is an
  // agent-prepared document headed to a federal agency behind an attention panel
  // the agent must acknowledge before it will generate. So the KIND is pinned here
  // rather than the tab merely being hidden — a hidden tab still leaves the state
  // reachable, and `printKind` persists across a portal/agency render of the same
  // component.
  const printKindEff = isPortal ? "maps" : printKind;
  const [fsa, setFsa] = useState(null);                 // FSA planting report payload
  const [fsaBusy, setFsaBusy] = useState(false);
  const [fsaAck, setFsaAck] = useState(false);          // reviewed the attention panel
  const [fsaCrops, setFsaCrops] = useState(() => new Set()); // "" = every crop
  const [printOpen, setPrintOpen] = useState(false);
  const [openFarms, setOpenFarms] = useState(() => new Set()); // farmers expanded to show their fields
  const [focusClu, setFocusClu] = useState(null);         // CLU to zoom the map to (field picked in tree)
  // crop_year → farmer_id → { byCrop:{crop:{bu,acres,liab}}, revGuar }. Kept per year:
  // crop_coverage holds every year's policies at once, and summed together the strip
  // showed 2026 and 2027 guarantees as one number.
  const [guarByYear, setGuarByYear] = useState(null);
  const guar = useMemo(() => (guarByYear ? (guarByYear[String(cropYear)] || {}) : null), [guarByYear, cropYear]);
  const [measuring, setMeasuring] = useState(false);      // area-measure mode: click the map to drop polygon vertices
  const [mpts, setMpts] = useState([]);                   // measured polygon vertices (L.LatLng)
  const [showPrfGrids, setShowPrfGrids] = useState(false);// overlay the RMA PRF rainfall-index grid cells
  const [prfColorBy, setPrfColorBy] = useState(false);    // false = boundaries+IDs only; true = fill by interval rainfall
  const [prfIv, setPrfIv] = useState(() => currentPrfInterval()); // which PRF interval to color by
  const [prfData, setPrfData] = useState(null);           // { locs:[], byGridIv:{} } for CURRENT_CROP_YEAR
  const [prfDaily, setPrfDaily] = useState(null);         // grid_id -> received inches for the SELECTED interval (fresh, all grids)
  const [prfDailyAsOf, setPrfDailyAsOf] = useState(null); // last obs_date those inches run through (the feed lags a day or two)
  const prfLayerRef = useRef(null);
  const [showFarmerBounds, setShowFarmerBounds] = useState(false); // overlay the farmer's own imported field boundaries
  const [farmerBounds, setFarmerBounds] = useState(null);          // loaded farmer_field_boundaries rows
  const farmerBoundsRef = useRef(null);
  const [showParcels, setShowParcels] = useState(false);  // overlay NYS tax parcels (landowner lookup)
  const [parcels, setParcels] = useState(null);           // { status, features } for the CURRENT viewport only
  const parcelsRef = useRef(null);
  // Radar — region-wide, observed through now then HRRR forward to +18h on one
  // 15-minute timeline. Off by default: it is the heaviest overlay on the page
  // (one PNG per frame) and most Field Mapper sessions never want it.
  const [showRadar, setShowRadar] = useState(false);
  const [radarMan, setRadarMan] = useState(null);         // { frames, nowFrame, run, ... } from ?fn=radar&op=manifest
  const [radarIdx, setRadarIdx] = useState(0);            // index into radarMan.frames
  const [radarPlay, setRadarPlay] = useState(false);
  const [radarReady, setRadarReady] = useState(0);        // how many frames have finished preloading
  const [radarAgree, setRadarAgree] = useState(null);     // leadMin -> run-to-run agreement %
  const [radarOpacity, setRadarOpacity] = useState(0.7);
  const [radarBbox, setRadarBbox] = useState(null);       // snapped [w,s,e,n] the frames were rendered for
  // "single" draws one HRRR run. "prob" draws the time-lagged ensemble — several
  // recent runs all valid at the same instant — smoothed to the scale at which
  // those runs actually agree. See api/_radar.js: at pixel precision they agree
  // ~0.2% of the time, which is why a single crisp run reads as "very wrong".
  const [radarMode, setRadarMode] = useState("single");
  const [radarSpread, setRadarSpread] = useState(null);   // leadMin -> { radiusKm, members, reachedConsensus }
  const [radarTick, setRadarTick] = useState(0);          // bumped by the refresh timer to re-pull the manifest
  // Forecast rain/snow TOTALS. Deliberately a separate overlay from radar rather
  // than another radar mode: radar is an instant on a timeline, an accumulation
  // is an integral over a window. They answer different questions and share no
  // controls. Mutually exclusive with radar — stacked, the two colour fields turn
  // to mud and neither can be read off its key.
  const [showPrecip, setShowPrecip] = useState(false);
  // ⚠ MACHINE YIELD AS A LAYER, on the CLU the whole map is already keyed on.
  // cluYield has been fetched and shown on hover for a while; this is the step
  // that lets you LOOK at it across a farm instead of one field at a time.
  const [paintBy, setPaintBy] = useState("crop");   // crop | yield
  // Per-CROP ramp bounds. One shared scale would paint every soybean field as
  // poor against corn, and corn SILAGE is ton/ac against grain's bu/ac — the
  // never-blend-units rule, on a colour ramp.
  const yieldScale = React.useMemo(() => {
    const by = {};
    if (!cluYield) return by;
    for (const k of Object.keys(cluYield)) {
      const y = cluYield[k];
      if (!y || !(y.value > 0)) continue;
      const c = String(y.crop || "").toUpperCase();
      (by[c] = by[c] || []).push(y.value);
    }
    const out = {};
    for (const c of Object.keys(by)) {
      const v = by[c].slice().sort((a, b) => a - b);
      // 10th/90th rather than min/max: one divide-by-zero blowup would flatten
      // the whole ramp. The plausibility guard upstream catches the worst, but a
      // percentile band is what keeps a single odd field from setting the scale.
      const q = (f) => v[Math.max(0, Math.min(v.length - 1, Math.round((v.length - 1) * f)))];
      out[c] = { lo: q(0.10), hi: q(0.90), n: v.length };
    }
    return out;
  }, [cluYield]);
  const yieldCount = React.useMemo(
    () => Object.values(yieldScale).reduce((a, x) => a + (x.n || 0), 0), [yieldScale]);
  // green ramp, low → high, within one crop
  const yieldFill = (crop, v) => {
    const sc = yieldScale[String(crop || "").toUpperCase()];
    if (!sc || !(v > 0) || !(sc.hi > sc.lo)) return null;
    const t = Math.max(0, Math.min(1, (v - sc.lo) / (sc.hi - sc.lo)));
    const stops = ["#d9534f", "#e8a33d", "#e8d54a", "#8fbc5a", "#3f8f3f", "#1f5c2e"];
    return stops[Math.min(stops.length - 1, Math.floor(t * stops.length))];
  };
  const [precipField, setPrecipField] = useState("rain");   // rain | snow
  const [precipHours, setPrecipHours] = useState(24);
  const [precipWins, setPrecipWins] = useState(null);       // from ?fn=precip&op=windows
  const precipLayerRef = useRef(null);
  // The values behind the picture. A colour ramp can't be read back to a number,
  // and "will this field get half an inch" is the actual question — so the grid
  // is fetched once per window and sampled locally. Locally, because hover does
  // not exist on a phone: the readout has to answer a TAP, and a tap that waits
  // on a round trip feels broken.
  const [precipGrid, setPrecipGrid] = useState(null);
  const [precipProbe, setPrecipProbe] = useState(null);     // { lat, lon, inches } — sticky, set by tap/click
  const [precipHover, setPrecipHover] = useState(null);     // desktop-only live value under the cursor
  const precipMarkRef = useRef(null);
  // Arrival window: at 6h+ the dominant error is TIMING, not placement, and
  // every other layer here smooths in space and says nothing about when. Same
  // tap interaction as the totals readout, for the same reason (no hover on a phone).
  // Day-by-day rain for the tapped point: what day, and how much. Chance comes
  // from NBM's own calibrated probability of measurable precip; amount from the
  // deterministic buckets. Deliberately two separate figures — see _precip.js.
  const [precipDaily, setPrecipDaily] = useState(null);
  const [precipDailyBusy, setPrecipDailyBusy] = useState(false);
  // Save the tapped point straight to the Weather page. Typing coordinates into a
  // separate page is the worst way to record a place you are already standing on.
  const [watchName, setWatchName] = useState(null);   // null = not naming; "" = naming
  const [watchSaved, setWatchSaved] = useState(false);
  const [radarArrival, setRadarArrival] = useState(null);   // from ?fn=radar&op=arrival
  const [radarProbe, setRadarProbe] = useState(null);       // { lat, lon, runs, total, firstH, lastH }
  const radarLayerRef = useRef(null);
  const radarCacheRef = useRef(new Map());                // frame url -> HTMLImageElement (browser-cache warm)
  // John Deere Operations Center overlays (SEPARATE from our FSA/RMA CLU layer).
  const [showJdBounds, setShowJdBounds] = useState(false);// JD field boundaries + planting/harvest
  const [showJdPlanting] = useState(false); // (retired) color JD fields by planted crop — toggle removed to compact the overlay bar
  const [jdFields, setJdFields] = useState(null);         // GeoJSON FeatureCollection from jd-fieldmap
  const [jdBusy, setJdBusy] = useState(false);
  const [jdSel, setJdSel] = useState(null);               // clicked field's properties (year picker source)
  // "🚜 Yield map" clicked on an INSURED field's popup. Parked here rather than
  // acted on directly because the JD layer is usually still off at that moment —
  // its boundaries have to arrive before a CLU can be matched to a JD field.
  const [jdWant, setJdWant] = useState(null);             // { lat, lng, name } pending CLU→JD resolution
  const jdFetchRef = useRef(0);                           // seq of the newest jd-fieldmap request
  const jdFieldsSeqRef = useRef(0);                       // seq the data now in jdFields answers
  const [jdGrids, setJdGrids] = useState({});             // op_id -> yield grid
  // Agent-entered true totals, so an uncalibrated monitor can be scaled to what the crop
  // actually made. Small table, read straight — the factor is derived here rather than
  // shipped from the API because the map loads grids one operation at a time.
  const [jdCal, setJdCal] = useState([]);                 // yield_calibrations rows
  const jdCalFactor = (crop, dateish) => {
    const yr = Number(String(dateish || "").slice(0, 4));
    const c = (jdCal || []).find(x => String(x.crop || "").toUpperCase() === String(crop || "").toUpperCase()
                                   && Number(x.crop_year) === yr);
    return (c && +c.factor > 0) ? { ...c, factor: +c.factor } : null;
  };
  const [jdOp, setJdOp] = useState(null);                 // op_id currently rendered as heatmap
  const [jdLoading, setJdLoading] = useState(null);       // op_id being generated (first time)
  const [jdMsg, setJdMsg] = useState("");                 // yield-load status (visible diagnostic)
  const [jdSearch, setJdSearch] = useState("");           // field-name search box
  const [jdHover, setJdHover] = useState(null);           // { v, x, y } yield under the cursor
  const [jdContour, setJdContour] = useState(true);       // vector contour render (on) vs raw raster pixels (off) — on by default
  const [d3Ready, setD3Ready] = useState(typeof window !== "undefined" && !!window.d3);
  useEffect(() => { if (!d3Ready) d3Promise.then(() => setD3Ready(true)).catch(() => {}); }, [d3Ready]);
  // ── SOIL SAMPLES ──────────────────────────────────────────────────────────
  // Sampling events + their per-zone geometry. An event may legitimately carry NO
  // geometry at all (a PDF-only lab report), so the panel lists every event for the
  // field while only the mappable ones can draw. Fetched per selected farm — the
  // Field Mapper has crashed on an oversized payload before, so this never goes
  // book-wide.
  const [showSoil, setShowSoil] = useState(false);
  const [soilEvents, setSoilEvents] = useState(null);     // [{event, samples:[…]}]
  const [soilBusy, setSoilBusy] = useState(false);
  const [soilEventId, setSoilEventId] = useState(null);   // which event is drawn
  const [soilAnalyte, setSoilAnalyte] = useState("PH");
  const [soilSel, setSoilSel] = useState(null);           // clicked zone → readout
  const soilLayerRef = useRef(null);
  // Calibration factors, loaded once. The whole table is a handful of agent-entered rows and
  // the view does the arithmetic, so there is no per-farm fetch and no client-side maths that
  // could disagree with the Machine Data tables.
  // AGENCY ONLY. Calibration is our working-out, not the grower's, and the underlying table
  // has no farmer-scoped policy by design — so the surface is hidden here as well as being
  // closed at the database (the view is security_invoker, so a portal token would get
  // nothing anyway). Hide the surface AND guard the fetch, per the portal rule.
  useEffect(() => {
    if (isPortal) { setJdCal([]); return; }
    let alive = true;
    // CHEAP PROBE FIRST. yield_calibration_factors is a VIEW that aggregates jd_field_ftf and
    // policy_fields — ~18k rows scanned — and it was being run on EVERY map open whether or
    // not a single calibration existed, which for now is never. Ask the tiny base table
    // whether there is anything to compute, and only then pay for the view. Written
    // unbounded on 2026-08-11 and caught the same day while working out why the database
    // fell over twice: adding queries to hot paths without pricing them is how you get there.
    (async () => {
      try {
        const any = await sbFetch("yield_calibrations", { select: "id", limit: 1 });
        if (!alive || !any || !any.length) { if (alive) setJdCal([]); return; }
        const rows = await sbFetch("yield_calibration_factors", { select: "farmer_id,crop_year,crop,factor,target_yield,monitor_yield,coverage_pct,total_bushels,source" });
        if (alive) setJdCal(rows || []);
      } catch { if (alive) setJdCal([]); }
    })();
    return () => { alive = false; };
  }, [isPortal]);
  const [jdFieldScale, setJdFieldScale] = useState(null); // { lo, hi } manual scale for the single-field map
  const jdBoundsRef = useRef(null);
  const jdYieldRef = useRef(null);
  // Whole-operation comparison — every field of one crop-year on ONE fixed scale.
  const [jdOpsOn, setJdOpsOn] = useState(false);          // operation-compare mode toggle
  const [jdOpsFarmer, setJdOpsFarmer] = useState("");     // which operation (farmer_id)
  const [jdOpsList, setJdOpsList] = useState(null);       // [{farmer_id, name}] connected operations
  const [jdOpsManifest, setJdOpsManifest] = useState(null); // [{crop, year, fields}] available combos
  const [jdOpsSel, setJdOpsSel] = useState({ crop: "", year: "" });
  const [jdOpsData, setJdOpsData] = useState(null);       // { grids, lo, hi, mean, min, max }
  const [jdOpsBusy, setJdOpsBusy] = useState(false);
  const [jdOpsScale, setJdOpsScale] = useState(null);     // { lo, hi } manual override of the auto scale
  const jdOpsLayersRef = useRef([]);

  // Boundaries for the CLUs this viewer can actually see. `allClus` is a sorted
  // comma string (stable identity), so this fires once per real change of the
  // field set — not on every filter/label edit.
  useEffect(() => {
    let alive = true;
    loadFieldGeomsFor(allClus ? allClus.split(",") : [])
      .then(m => { if (alive) setGeoms(m); })
      .catch(() => { if (alive) setGeoms({}); });
    return () => { alive = false; };
  }, [allClus]);

  useEffect(() => {
    let alive = true;
    loadLeaflet().then(LL => { if (alive) setL(LL); }).catch(() => { if (alive) setMapFail(true); });
    // Insurance guarantees per farmer, per crop. Base rows (RP/YP/APH) carry
    // APH + acres; overlays (ECO/SCO/ARPI) don't — skip those so acres aren't
    // double-counted (the SCO/ECO liability sits on the same base acres).
    sbFetch("crop_coverage", { select:"crop,plan,type,practice,price_election,aph_yield,coverage_level,acres,liability,policies(farmer_id,crop_year)" })
      .then(rows => {
        const Y = {};
        for (const r of (rows||[])) {
          const fid = r.policies && r.policies.farmer_id; if (!fid) continue;
          const cy = r.policies.crop_year; if (cy == null) continue;
          const m = Y[String(cy)] || (Y[String(cy)] = {});
          const plan = (r.plan||"").toUpperCase();
          if (plan.includes("ECO") || plan.includes("SCO") || plan === "ARPI") continue; // base plans only
          const crop = r.crop || "?";
          if (!m[fid]) m[fid] = { byCrop:{}, revGuar:0 };
          const bc = m[fid].byCrop[crop] || (m[fid].byCrop[crop] = { bu:0, liab:0, acres:0, plans:new Set(), counties:new Set(), _aphW:0, _covW:0, _w:0 });
          // Acres, plan and acre-weighted APH / coverage level, for the inspector's
          // per-operation rollup. APH must be ACRE-weighted, not a simple mean —
          // crop_coverage is stored per (county × crop × plan), so a 12-acre county
          // would otherwise pull the number as hard as a 900-acre one.
          {
            const ac = parseFloat(r.acres);
            const aph = parseFloat(r.aph_yield);
            const cov = parseFloat(r.coverage_level);
            if (Number.isFinite(ac) && ac > 0) {
              bc.acres += ac;
              if (Number.isFinite(aph)) { bc._aphW += aph * ac; bc._w += ac; }
              if (Number.isFinite(cov)) bc._covW += cov * ac;
            }
            if (plan) bc.plans.add(plan);
            if (r.county) bc.counties.add(normalizeCounty(r.county));
          }
          // Revenue guarantee = M13 liability (acre-weighted, authoritative).
          // Bushel guarantee = liability ÷ price (= SOI "Marketable Guarantee");
          // APH × cov × acres only as a fallback when there's no liability yet.
          const liab = r.liability != null ? Number(r.liability) : null;
          const { price, unit } = getCropPriceAndUnit(r, prices, Number(cy));
          // Divide by the ELECTED price (price x price-election percent), not the
          // price. A CAT row elects 55%, so liability / price alone reads ~45% short.
          const effPrice = electedPrice(price, unit, r);
          if (liab != null) {
            bc.liab += liab; m[fid].revGuar += liab;
            if (effPrice > 0) bc.bu += liab / effPrice;
          } else {
            const aph = parseFloat(r.aph_yield), cov = parseFloat(r.coverage_level), ac = parseFloat(r.acres);
            if (Number.isFinite(aph) && Number.isFinite(cov) && Number.isFinite(ac)) bc.bu += aph * cov * ac;
          }
        }
        // Resolve the weighted accumulators into plain numbers once.
        for (const cy in Y) for (const fid in Y[cy]) for (const crop in Y[cy][fid].byCrop) {
          const bc = Y[cy][fid].byCrop[crop];
          bc.aph = bc._w > 0 ? bc._aphW / bc._w : null;
          bc.cov = bc._w > 0 ? bc._covW / bc._w : null;
          bc.plan = [...bc.plans].sort().join(" / ") || null;
          bc.countyList = [...bc.counties].sort();
          delete bc._aphW; delete bc._covW; delete bc._w; delete bc.plans; delete bc.counties;
        }
        if (alive) setGuarByYear(Y);
      }).catch(() => { if (alive) setGuarByYear({}); });
    return () => { alive = false; };
  }, []);   // eslint-disable-line

  // init Leaflet map once L + container ready
  useEffect(() => {
    if (!L || !mapEl.current || mapRef.current) return;
    // Clicking a vector field gives its SVG <path> keyboard focus, and the browser
    // draws a default focus outline = a black bounding-box rectangle around the
    // field (looks like a phantom second boundary). Suppress it globally, once.
    if (!document.getElementById("leaflet-nofocus-style")) {
      const st = document.createElement("style");
      st.id = "leaflet-nofocus-style";
      st.textContent = ".leaflet-interactive:focus{outline:none;}";
      document.head.appendChild(st);
    }
    // Restore the exact prior center/zoom when returning (e.g. Back from the Weather
    // page); default WNY view on the first-ever mount.
    const iv = initialView && Array.isArray(initialView.center) ? initialView : { center:[42.9, -77.85], zoom:9 };
    const map = L.map(mapEl.current, { zoomControl:true, scrollWheelZoom:true }).setView(iv.center, iv.zoom);
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxZoom:19, attribution:"Imagery © Esri, Maxar, Earthstar Geographics" }).addTo(map);
    mapRef.current = map;
    // Report the live viewport up so the parent can restore it after we unmount.
    if (onViewport) { const report = () => { try { const c = map.getCenter(); onViewport({ center:[c.lat, c.lng], zoom: map.getZoom() }); } catch (e) {} }; map.on("moveend", report); map.on("zoomend", report); }
    setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 60);
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [L]); // eslint-disable-line

  // County boundary overlay — dashed lines for just the states in the book.
  // Non-interactive so it never intercepts a field click, and sent to the back
  // so the crop-colored fields always draw on top.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    const states = [...new Set(rows.map(r => String(r.state_fips || "").padStart(2, "0")).filter(s => s.length === 2 && s !== "00"))];
    if (!states.length) return;
    let alive = true;
    Promise.all(states.map(loadCountyGeoForState)).then(results => {
      if (!alive || !mapRef.current) return;
      const feats = results.filter(Boolean).flatMap(gj => gj.features || []);
      if (!feats.length) return;
      if (countyRef.current) { map.removeLayer(countyRef.current); countyRef.current = null; }
      const layer = L.geoJSON({ type: "FeatureCollection", features: feats }, {
        interactive: false,
        style: { color: "#ffd34d", weight: 1.2, opacity: 0.85, fill: false, dashArray: "5,4" },
      }).addTo(map);
      try { layer.bringToBack(); } catch (e) {}
      countyRef.current = layer;
    });
    return () => { alive = false; };
  }, [rows, L]);

  // GeoJSON for the in-view rows, respecting the farm selection.
  // A single CLU polygon can be SPLIT-PLANTED across crops (e.g. one field = 15 ac
  // wheat + 28 ac soy under one CLU). The AIP feed gives acres per crop but NOT the
  // internal boundary, so we draw ONE polygon per CLU carrying all its crops, and
  // render multi-crop CLUs with a two-color stripe (see ensureCropStripes) instead
  // of silently painting the whole field one crop's color and hiding the rest.
  const fc = useMemo(() => {
    if (!geoms) return null;
    const feats = [], byClu = new Map(), points = [], seenPt = new Set();
    const accPP = (agg, r) => {   // aggregate prevent-plant + acres across the CLU's crop rows
      const ppAc = r.pp_acres != null ? Number(r.pp_acres) : 0;
      const ac = r.acres != null ? Number(r.acres) : 0;
      // Uninsurable acreage is summed across the CLU's crop rows exactly as PP
      // is — one physical field can carry two crops and the AIP can call either
      // of them uninsurable.
      agg.uiAc += (r.ui_acres != null ? Number(r.ui_acres) : 0);
      agg.ppAc += ppAc; agg.totAc += ac;
      const c = r.crop || "—";
      const cur = agg.crops.get(c) || { crop: c, acres: 0, planted: null, ppAc: 0, type_code: null };
      cur.acres += ac; cur.ppAc += ppAc;
      if (!cur.planted && r.report_date) cur.planted = r.report_date;
      if (!cur.type_code && r.type_code) cur.type_code = r.type_code;   // 016 GSG / 026 GSS
      agg.crops.set(c, cur);
    };
    for (const r of rows) {
      if (selSet.size && !selSet.has(r.farmer_id)) continue;
      const key = (r.clu_id||"").toLowerCase();
      const fname = (r.farmers && r.farmers.name) || "";
      if (key && geoms[key]) {
        let agg = byClu.get(key);
        if (!agg) {
          agg = { key, farmer:fname, farmer_id:r.farmer_id, fsn:r.fsn, tract:r.tract, field:r.field_id, field_id:r.field_id,
            county:r.insured_county||r._physCounty||r.county, state:fieldStateOf(r), clu_id:r.clu_id, latitude:r.latitude, longitude:r.longitude, policy_number:r.policy_number,
            fieldAc: (r.acres != null ? Number(r.acres) : (cluAcres[key] ?? null)), crops:new Map(), totAc:0, ppAc:0, uiAc:0 };
          byClu.set(key, agg);
        }
        accPP(agg, r);
      } else if (r.latitude != null && r.longitude != null) {
        // no boundary → a point marker per (clu,crop); keep the original per-crop dedup
        const sk = key + "|" + (r.crop||"") + "|" + r.latitude + "|" + r.longitude;
        if (seenPt.has(sk)) continue; seenPt.add(sk);
        points.push({ r, fname });
      }
    }
    // one polygon feature per CLU — with the full crop breakdown
    for (const agg of byClu.values()) {
      const crops = [...agg.crops.values()].sort((a,b) => (b.acres||0) - (a.acres||0));
      const plantedAc = Math.max(0, agg.totAc - agg.ppAc);
      const pp = agg.ppAc > 0, ppFull = pp && plantedAc <= 0.05, ppPartial = pp && plantedAc > 0.05;
      const props = { crop: (crops[0] && crops[0].crop) || "—", crops, multi: crops.length > 1,
        farmer:agg.farmer, farmer_id:agg.farmer_id, acres: agg.totAc > 0 ? agg.totAc : null, fieldAc:agg.fieldAc,
        fsn:agg.fsn, tract:agg.tract, field:agg.field, field_id:agg.field_id, county:agg.county, state:agg.state,
        planted: (crops[0] && crops[0].planted) || null, type_code: (crops[0] && crops[0].type_code) || null,
        pp, ppFull, ppPartial, ppAc:agg.ppAc, plantedAc,
        uiAc: agg.uiAc, uninsurable: fieldIsUninsurable(agg.uiAc, agg.totAc),
        label: labels[(agg.clu_id||"").toLowerCase()] || null,
        jdName: (jdNames[(agg.clu_id||"").toLowerCase()] || {}).name || null,
        jdFieldId: (jdNames[(agg.clu_id||"").toLowerCase()] || {}).jd_field_id || null,
        clu_id:agg.clu_id, latitude:agg.latitude, longitude:agg.longitude, policy_number:agg.policy_number };
      feats.push({ type:"Feature", geometry: rewindGeom(geoms[agg.key]), properties: props });
    }
    // point features (no boundary) — single crop each
    for (const { r, fname } of points) {
      const ppAc = r.pp_acres != null ? Number(r.pp_acres) : 0;
      const totAc = r.acres != null ? Number(r.acres) : 0;
      const uiAc = r.ui_acres != null ? Number(r.ui_acres) : 0;
      const plantedAc = Math.max(0, totAc - ppAc);
      const pp = ppAc > 0, ppFull = pp && plantedAc <= 0.05, ppPartial = pp && plantedAc > 0.05;
      const props = { crop:r.crop, crops:[{ crop:r.crop, acres:(r.acres!=null?Number(r.acres):null), planted:r.report_date }], multi:false,
        farmer:fname, farmer_id:r.farmer_id, acres:r.acres, fieldAc:(r.acres!=null?Number(r.acres):(cluAcres[(r.clu_id||"").toLowerCase()]??null)),
        fsn:r.fsn, tract:r.tract, field:r.field_id, field_id:r.field_id, county:r.insured_county||r._physCounty||r.county, state:fieldStateOf(r), planted:r.report_date, type_code:r.type_code || null,
        label: labels[(r.clu_id||"").toLowerCase()] || null,
        jdName: (jdNames[(r.clu_id||"").toLowerCase()] || {}).name || null,
        jdFieldId: (jdNames[(r.clu_id||"").toLowerCase()] || {}).jd_field_id || null,
        pp, ppFull, ppPartial, ppAc, plantedAc, uiAc, uninsurable: fieldIsUninsurable(uiAc, totAc),
        clu_id:r.clu_id, latitude:r.latitude, longitude:r.longitude, policy_number:r.policy_number };
      feats.push({ type:"Feature", geometry:{ type:"Point", coordinates:[+r.longitude, +r.latitude] }, properties: props });
    }
    return { type:"FeatureCollection", features:feats };
  }, [rows, geoms, selSet, cluAcres, labels, jdNames]);
  // ⚠ COUNTED OVER THE FIELDS ON SCREEN, not over the whole evidence map. `cluEvidence` is
  // built for every farm that has an appraisal; the map may be scoped to one farm, and a count
  // of 23 beside a map showing nine fields is a number about somebody else's farm.
  const evidenceCount = React.useMemo(() => {
    if (!cluEvidence || !fc) return 0;
    let n = 0;
    for (const f of fc.features || []) {
      const e = cluEvidence[String((f.properties || {}).clu_id || "").toLowerCase()];
      if (e && e.state !== "neither") n++;
    }
    return n;
  }, [cluEvidence, fc]);
  const anyPP = useMemo(() => (fc?.features||[]).some(f => f.properties.pp), [fc]);
  // ── CHOP-SELECT MODE ────────────────────────────────────────────────────────
  // The grower is picking the fields they intend to CHOP. While it is on, the map
  // stops being an inspector and becomes a picker: a click toggles the field instead
  // of opening its popup, so there is no way to "click a field" and get the wrong
  // thing.
  //
  // ONLY CORN IS SELECTABLE, and every other crop is dimmed rather than hidden —
  // hiding them would make a grower think the wheat had vanished from their acreage
  // report. A corn field with no reported type is selectable too: an untyped corn
  // line is NOT grain (see cornTypeLabel), and refusing it here would silently put
  // those acres out of reach exactly the way the Field Mapper's old grain/silage
  // filter did.
  const chopSelSet = chopSel || null;
  const isChoppable = (p) => String((p && p.crop) || "").toUpperCase() === "CORN"
    || (p && (p.crops || []).some((c) => String(c.crop || "").toUpperCase() === "CORN"));
  const chopKey = (p) => String((p && p.clu_id) || "").toLowerCase();
  // THE CORN ACRES ON THIS FIELD, NOT THE FIELD'S ACRES. `p.acres` is the sum of
  // EVERY insured line on the CLU (the polygon carries the whole crop breakdown), so
  // on a split-planted field it is corn + soybeans + wheat. Marking that field for
  // chopping only ever commits its CORN acres — the picker's own running total and
  // the plan the agent receives both come from the corn line — so a tooltip reading
  // the whole-CLU figure told the grower a different number from the one they were
  // about to save. Measured CY2026: 568 CLUs carry more than one crop, 465 of them
  // corn; Pagen's FSN 6949/2363/1 is 35.9 ac of corn on a 53.7 ac field it shares
  // with 17.8 ac of soybeans. Falls back to `p.acres` when the breakdown is absent
  // (point markers), which is single-crop by construction.
  const chopCornAcres = (p) => {
    const crops = (p && p.crops) || [];
    let ac = null;
    for (const c of crops) {
      if (String(c.crop || "").toUpperCase() !== "CORN") continue;
      if (c.acres != null) ac = (ac || 0) + Number(c.acres);
    }
    return ac != null ? ac : (p && p.acres != null ? Number(p.acres) : null);
  };
  // A field's INSURED type, which is what decides whether a choice is a change of
  // intent. 016 GSG / 026 GSS straight from the AIP — an untyped corn line is
  // neither, and must not be folded into grain (cornTypeLabel's rule).
  const chopInsuredType = (p) => {
    const t = String((p && p.type_code) == null ? "" : p.type_code).trim();
    if (t === "016" || t === "026") return t;
    for (const c of (p && p.crops) || []) {
      if (String(c.crop || "").toUpperCase() !== "CORN") continue;
      const ct = String(c.type_code == null ? "" : c.type_code).trim();
      if (ct === "016" || ct === "026") return ct;
    }
    return null;
  };
  const chopStyleFor = (p) => {
    if (!isChoppable(p)) {
      // Not corn — visibly present, plainly not pickable.
      return { color: "#ffffff", weight: 0.5, opacity: 0.25, fillColor: fieldCropColor(p.crop, p.type_code), fillOpacity: 0.14 };
    }
    const on = chopSelSet && chopSelSet.has(chopKey(p));
    // Selected reads as one obvious state — a saturated orange with a heavy white
    // rim. It is deliberately NOT the silage brown a 026 field already draws in,
    // because then a field insured as silage would look identical picked or not.
    if (on) return { color: "#ffffff", weight: 2.6, opacity: 1, fillColor: "#ff8c1a", fillOpacity: 0.86 };
    // A SILAGE-INSURED FIELD LEFT UNPICKED IS THE CASE THAT HIDES (Kevin, 2026-08-19).
    // Combining corn written as silage is as much a change of intent as chopping corn
    // written as grain, and it is the direction nobody notices, because it needs no
    // event — the chopper simply never comes. So it cannot look like an ordinary
    // unpicked field: it draws a red rim that says "you have not accounted for this
    // one". BRIDGEWATER and SPARTA are insured 100% as silage, so on those two farms
    // every field starts in this state and the map is the reminder.
    if (chopInsuredType(p) === "026") {
      return { color: "#c0392b", weight: 2.2, opacity: 1, fillColor: fieldCropColor("CORN", "026"), fillOpacity: 0.6, dashArray: "6,3" };
    }
    return { color: "#ffd9a0", weight: 1.3, opacity: 0.95, fillColor: fieldCropColor(p.crop, p.type_code), fillOpacity: 0.55, dashArray: "4,3" };
  };

  // (re)draw the field layer
  useEffect(() => {
    const map = mapRef.current; if (!map || !L || !fc) return;
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    if (!fc.features.length) return;
    const layer = L.geoJSON(fc, {
      // FULL prevent-plant → white dashed border + diagonal hatch fill (applied below).
      // PARTIAL PP → looks like a normal planted field (no map marker); the planted/PP
      // split shows on hover/click only. Planted → solid crop color.
      // Under radar the white borders are the problem, not the fills. At region
      // zoom there are thousands of small polygons, and a 1px white outline on
      // each reads as snow/static over the reflectivity — you can't tell field
      // from weather. So when radar is on the outline thins and fades while the
      // crop fill stays (dimmed): the field is still identifiable by colour, it
      // just stops competing with the layer you turned on to look at.
      style: f => {
        const p = f.properties;
        if (chopMode) return chopStyleFor(p);
        const dim = showRadar || showPrecip;
        // ── EVIDENCE: appraised / harvested / both / nothing yet ──────────────
        // ⚠ "NOTHING YET" DRAWS HOLLOW AND DASHED, never a colour at the bottom of a
        // ramp — the same rule the yield layer follows two branches down and the MI map's
        // hatching follows. A field nobody has spoken for is an ABSENCE of evidence, and a
        // solid fill would read as a measured state.
        if (paintBy === "evidence") {
          const e = cluEvidence ? cluEvidence[String(p.clu_id || "").toLowerCase()] : null;
          if (!e || e.state === "neither") {
            return { color: "#677167", weight: 1, opacity: 0.75, dashArray: "3,3",
                     fillColor: "#cfd8cf", fillOpacity: dim ? 0.10 : 0.16 };
          }
          // BOTH is the one that needs a decision, so it gets the heaviest rim — it is the
          // only state where two figures exist and one of them must be set aside.
          if (e.state === "both") {
            return { color: "#ffffff", weight: dim ? 1 : 2.4, opacity: dim ? 0.5 : 1,
                     fillColor: "#1a4a7a", fillOpacity: dim ? 0.45 : 0.82 };
          }
          // Appraised then CHOPPED on a grain policy: the appraisal is the figure (Kevin,
          // 2026-10-26), so it fills in the appraisal hue with a heavy rim.
          if (e.state === "both_chopped" || e.state === "both_unknown") {
            return { color: "#5a3a98", weight: dim ? 1 : 2.4, opacity: dim ? 0.5 : 1,
                     fillColor: "#d4a017", fillOpacity: dim ? 0.45 : 0.82 };
          }
          // Chopped with NO appraisal: no grain figure exists. Drawn as a hole, rimmed red.
          if (e.state === "chopped" || e.state === "harvest_unknown" || e.state === "silage_none") {
            return { color: "#a33", weight: dim ? 1 : 1.8, opacity: dim ? 0.5 : 0.95, dashArray: "5,2",
                     fillColor: "#f3d6cf", fillOpacity: dim ? 0.15 : 0.35 };
          }
          // Insured as silage and chopped: the JD chopper estimate (tons) is its figure. Its own
          // hue, never the "combined" green — a chopper pass is not a combine harvest.
          if (e.state === "silage_chopped") {
            return { color: "#ffffff", weight: dim ? 0.5 : 1, opacity: dim ? 0.35 : 0.9,
                     fillColor: "#1f7a7a", fillOpacity: dim ? 0.45 : 0.78 };
          }
          // A farm-number appraisal is a WEAKER answer than a field match, so it is drawn
          // as one — same hue, dashed rim — rather than being passed off as the same thing.
          if (e.state === "appraised") {
            return { color: "#ffffff", weight: dim ? 0.6 : (e.atFarmLevel ? 1.6 : 1),
                     opacity: dim ? 0.35 : 0.9, dashArray: e.atFarmLevel ? "4,3" : null,
                     fillColor: "#d4a017", fillOpacity: dim ? 0.4 : (e.atFarmLevel ? 0.55 : 0.78) };
          }
          return { color: "#ffffff", weight: dim ? 0.5 : 1, opacity: dim ? 0.35 : 0.9,
                   fillColor: "#2d6a2d", fillOpacity: dim ? 0.45 : 0.78 };
        }
        if (paintBy === "yield") {
          const y = cluYield ? cluYield[String(p.clu_id || "").toLowerCase()] : null;
          const fill = y ? yieldFill(y.crop, y.value) : null;
          // ⚠ NO MACHINE DATA IS NOT A LOW YIELD. Hollow + dashed, never the
          // bottom of the ramp — the same rule the MI map's hatching follows.
          if (!fill) return { color: "#677167", weight: 1, opacity: 0.75, dashArray: "3,3",
                              fillColor: "#cfd8cf", fillOpacity: dim ? 0.10 : 0.16 };
          return { color: "#ffffff", weight: dim ? 0.5 : 1, opacity: dim ? 0.35 : 0.9,
                   fillColor: fill, fillOpacity: dim ? 0.45 : 0.8 };
        }
        if (p.uninsurable) return { color:"#5b6470", weight: dim ? 0.7 : 1.4, opacity: dim ? 0.4 : 0.95, dashArray:"2,3", fillColor: UNINSURABLE_FILL, fillOpacity: dim ? 0.3 : 0.5 };
        if (p.ppFull) return { color:"#ffffff", weight: dim ? 0.7 : 1.4, opacity: dim ? 0.4 : 0.95, dashArray:"5,3", fillColor: fieldCropColor(p.crop, p.type_code), fillOpacity: dim ? 0.32 : 0.55 };
        return { color:"#ffffff", weight: dim ? 0.5 : 1, opacity: dim ? 0.35 : 0.9, fillColor: fieldCropColor(p.crop, p.type_code), fillOpacity: dim ? 0.4 : 0.7 };
      },
      pointToLayer: (f, latlng) => L.circleMarker(latlng, { radius:5, color: f.properties.uninsurable?"#5b6470":"#fff", weight: (f.properties.ppFull||f.properties.uninsurable)?1.6:1, dashArray: f.properties.uninsurable?"2,3":(f.properties.ppFull?"3,2":null), fillColor: f.properties.uninsurable?UNINSURABLE_FILL:fieldCropColor(f.properties.crop, f.properties.type_code), fillOpacity: f.properties.ppFull?0.6:0.95 }),
      onEachFeature: (f, lyr) => {
        const p = f.properties;
        const pd = p.planted ? `${p.planted.slice(5,7)}/${p.planted.slice(8,10)}/${p.planted.slice(0,4)}` : "";
        const status = p.uninsurable ? `<br><b style="color:#c9d3df">▨ UNINSURABLE</b> <span style="opacity:.8">${(p.uiAc||0).toFixed(1)} ac — no guarantee on this crop line</span>`
          : p.ppFull ? `<br><b style="color:#ffd34d">⬚ PREVENT PLANT</b> <span style="opacity:.8">whole field · ${(p.ppAc||0).toFixed(1)} ac</span>`
          : p.ppPartial ? `<br><b style="color:#ffb74d">⬚ PARTIAL PP</b> <span style="opacity:.85">${(p.plantedAc||0).toFixed(1)} planted · ${(p.ppAc||0).toFixed(1)} PP</span>${pd?` · planted ${pd}`:""}`
          : (p.multi ? "" : (pd ? `<br>Planted ${pd}` : ""));
        // Per-field acres: AIP-insured, else the shapefile REP_ACRES (covers R&H PRF
        // that has a CLU boundary). Fields with no boundary have no per-field acres.
        // A bare "24.7 ac" beside a sibling field reading "24.7 ac insured" invites
        // the reader to treat them as the same kind of number. It is not: the AIP
        // reported nothing for this field and this is the CLU boundary's own
        // measurement.
        // A grower reads "field boundary": CLU is our word for it.
        const acText = p.acres != null ? `${Number(p.acres).toFixed(1)} ac insured`
          : (p.fieldAc != null ? (isPortal ? `${Number(p.fieldAc).toFixed(1)} ac from the field boundary` : `${Number(p.fieldAc).toFixed(1)} ac from CLU boundary`) : "acres not reported per field");
        const shortD = (d) => d ? `${d.slice(5,7)}/${d.slice(8,10)}` : "";
        // Multi-crop CLU (split-planted field): list every crop + its acres. We have
        // acres per crop but not the internal boundary, so we're honest about that.
        // corn grain (GSG) vs silage (GSS) tag, from the per-field type
        const cornTag = String(p.crop||"").toUpperCase()==="CORN" && p.type_code
          ? ` <span style="opacity:.8">· ${p.type_code==="026" ? "Silage (GSS)" : "Grain (GSG)"}</span>` : "";
        const cropLine = p.multi
          ? `<b>${(p.crops||[]).map(c => `${c.crop} ${c.acres!=null?Number(c.acres).toFixed(1):"?"}${c.planted?` (${shortD(c.planted)})`:""}`).join(" + ")} ac</b> <span style="opacity:.75">· split-planted field, ${Number(p.acres||0).toFixed(1)} ac total</span>`
          : `${p.crop||"—"}${cornTag} — ${acText}`;
        const esc = (s) => String(s==null?"":s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
        // Custom field name (if set) headlines the popup/tooltip; FTF drops to a subtitle.
        const nameHdr = p.label ? `<div style="font-weight:800;color:#1a2e1a;font-size:13px;margin-bottom:2px">${esc(p.label)}</div>` : "";
        // The last line of the hover card is what the GROWER calls this field, when
        // their farm is connected to Ops Center (Kevin, 2026-08-14). It replaces the
        // machine yield that used to sit here: hovering a field mid-conversation, the
        // useful thing is "this is CR OEH and Fisher Rd", not a bushel figure from a
        // prior season — that lives on the Yield layer and the Machine Data tab, both
        // a click away. `· John Deere` is stated because the reader otherwise cannot
        // tell THEIR name for the field from one of ours.
        //
        // A field with no JD name keeps the yield rather than losing the line
        // entirely. In practice the two barely overlap — a field with no JD boundary
        // usually has no JD harvest either — so this is a fallback, not a mixed card.
        const _y = cluYield ? cluYield[String(p.clu_id || "").toLowerCase()] : null;
        const lastLine = p.jdName
          ? `<br><span style="color:#1f5480;font-weight:800">${esc(p.jdName)}</span><span style="opacity:.7"> · John Deere name</span>`
          : (_y && _y.value > 0)
            ? `<br><span style="color:#1f5480;font-weight:800">${esc(jdYieldLabel(_y.crop, _y.value, _y.unit))}</span>` +
              `<span style="opacity:.75"> ${esc(_y.crop || "")}${_y.date ? ` · ${String(_y.date).slice(0, 4)}` : ""} · ${jdHarvestWord(_y.unit)}</span>`
            : "";
        const info = `${nameHdr}<b>${esc(p.farmer||"")}</b><br>${cropLine}<br>Farm ${p.fsn||"—"} · Tract ${p.tract||"—"} · Field ${p.field||"—"}${status}${lastLine}`;
        // Non-sticky tooltip (anchored, not mouse-chasing) so it doesn't fight the
        // popup and flicker when the popup is open.
        // No hover card on a phone: a tap shows the tooltip AND opens the field, and the
        // tooltip then sits over the very field the sheet is about.
        if (!phoneRef.current) lyr.bindTooltip(info, { sticky:false });
        // Drive the inspector from OUR insured field. This is separate from jdSel,
        // which holds John Deere BOUNDARY properties (field, jd_field_id, harvests,
        // harvest_yield) — a completely different shape. The inspector needs clu_id /
        // farmer / county / fsn / tract / acres / pp, which only exist here.
        // While the chop picker is on, a click PICKS the field — it does not open the
        // inspector or the popup. Nothing below this point binds while chopMode is on.
        if (chopMode) {
          if (isChoppable(p) && chopKey(p) && onChopToggle) {
            lyr.on("click", () => onChopToggle(chopKey(p), p));
            const cornAc = chopCornAcres(p);
            const otherAc = (p.acres != null && cornAc != null) ? Number(p.acres) - cornAc : 0;
            lyr.bindTooltip(`${esc(p.label || p.jdName || "")}${p.label || p.jdName ? "<br>" : ""}Farm ${p.fsn||"—"} · Tract ${p.tract||"—"} · Field ${p.field||"—"}<br><b>${cornAc!=null?cornAc.toFixed(1):"?"} ac of corn</b>${otherAc > 0.05 ? ` <span style="opacity:.75">(of ${Number(p.acres).toFixed(1)} ac — the rest is another crop)</span>` : ""} · click to mark or unmark for chopping`, { sticky:false });
          }
          return;
        }
        // A click opens the FIELD CARD (rendered over the map, below) — the JD Ops
        // pattern Kevin pointed at, replacing a small Leaflet popup of HTML-string
        // buttons. It also scopes the farm, as the popup did, so switching to
        // Acreage / Weather / History stays on this grower.
        lyr.on("click", (e) => {
          if (e && e.originalEvent && L.DomEvent) L.DomEvent.stopPropagation(e);
          let center = null;
          try { center = lyr.getBounds ? lyr.getBounds().getCenter() : (lyr.getLatLng ? lyr.getLatLng() : null); } catch (_) { center = null; }
          setSelField({ p, geom: f.geometry || null, center });
          // Only when there is a choice to make: a one-farm view selecting its own farm
          // just adds a second stats strip and pushes the map down a phone screen.
          if (onSelectFarm && p.farmer_id && farms && farms.length > 1) onSelectFarm(p.farmer_id);
        });
      },
    }).addTo(map);
    layerRef.current = layer;
    // Fill prevent-plant polygons with a crop-colored diagonal hatch. Done after
    // addTo so the renderer's <svg> + each path exist; pattern injected lazily.
    try {
      const svg = mapEl.current && mapEl.current.querySelector(".leaflet-overlay-pane svg");
      if (svg) layer.eachLayer(lyr => {
        const p = lyr.feature && lyr.feature.properties;
        if (!p || !lyr._path) return;
        if (p.ppFull) {                                   // PP takes visual precedence
          const id = ensureFieldHatch(svg, fieldCropColor(p.crop, p.type_code));
          lyr._path.setAttribute("fill", `url(#${id})`);
          lyr._path.setAttribute("fill-opacity", "1");
        } else if (p.multi) {                             // split-planted → two-color stripes
          const cols = (p.crops || []).map(c => fieldCropColor(c.crop, c.type_code));
          const id = ensureCropStripes(svg, cols);
          lyr._path.setAttribute("fill", `url(#${id})`);
          lyr._path.setAttribute("fill-opacity", "0.9");
        }
      });
    } catch (e) {}
    // Partial-PP fields carry no map marker (it cluttered when zoomed out) — the
    // planted/PP split shows on hover/click via the field popup instead.
    // Only auto-fit when the field SET changes (filter/selection) — NOT when the layer
    // rebuilt merely because a field name/label changed, which would yank the zoom.
    const fitKey = fc.features.map(f => f.properties.clu_id || `${f.properties.fsn}-${f.properties.field_id}`).sort().join("|");
    // On a phone, a field already open in the sheet is also an explicit aim (only while it is still on the map): tapping a
    // field on the all-farms map scopes to its farm, the field set changes, and the
    // farm-wide fit would otherwise yank the map away from the field being read.
    const openClu = phoneRef.current && selFieldRef.current && selFieldRef.current.p ? cluLower(selFieldRef.current.p.clu_id) : "";
    const stillShown = !!openClu && fc.features.some(f => cluLower(f.properties.clu_id) === openClu);
    if (fitKey !== fitKeyRef.current) {
      fitKeyRef.current = fitKey;
      // ⚠ AN EXPLICIT "take me to THIS field" OUTRANKS THIS AUTOMATIC FIT, and the ordering is
      // not something to leave to luck. Arriving from Crop Review the field set changes in the
      // same breath as the focus — the farm is selected, so `fc` narrows and this auto-fit
      // fires — and whichever fitBounds ran last won. The fit key is still RECORDED so this
      // does not re-fire the moment the focus clears.
      //
      // ⚠ IT READS THE PENDING PROP, NEVER `focusClu`. The state is set by the tree picker too
      // and is never reset, so testing it would suppress the auto-fit for the rest of the
      // session after any single field click — a filter change would then quietly stop
      // re-framing the map. The prop is cleared the moment the focus is consumed, which is
      // exactly the window this needs to cover, and the zoom effect below lands in the commit
      // straight after.
      const aimed = focus && focus.clu_id;
      if (!stillShown) if (!aimed) { try { map.fitBounds(layer.getBounds(), fitOpts(24, 15)); } catch (e) {} }
    }
    // cluYield is a dependency because the hover card reads it — without it the
    // tooltip would keep rendering before the yields arrive.
  }, [fc, L, cluYield, cluEvidence, showRadar, showPrecip, chopMode, paintBy, yieldScale]);   // eslint-disable-line

  // Restyle the picked fields IN PLACE. A rebuild per click would re-run L.geoJSON
  // over thousands of polygons and make selection feel broken on a region-zoom map,
  // so only the fills move here — the layer itself is rebuilt only when chopMode
  // flips, because that changes what is BOUND to each field, not just its colour.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || !chopMode) return;
    layer.eachLayer((l) => {
      const p = l.feature && l.feature.properties;
      if (p && l.setStyle) l.setStyle(chopStyleFor(p));
    });
  }, [chopSel, chopMode]);   // eslint-disable-line

  // zoom to a single field when one is picked from the tree (runs after the
  // layer refit above, so the field zoom wins when both fire)
  useEffect(() => {
    const map = mapRef.current; if (!map || !L || !focusClu || !geoms || !geoms[focusClu]) return;
    try { const b = L.geoJSON({ type:"Feature", geometry: rewindGeom(geoms[focusClu]) }).getBounds(); map.fitBounds(b, fitOpts(50, 17)); } catch (e) {}
  }, [focusClu, geoms, L]);   // eslint-disable-line

  // External "locate this field" (from the Weather/History tab): select the farmer, then zoom
  // to the field — to its boundary if we have one, else pan to its centroid/lat-lon.
  useEffect(() => {
    if (!focus || !L) return;
    if (focus.farmer_id && onSelectFarm) onSelectFarm(focus.farmer_id);
    const key = cluLower(focus.clu_id);
    // ⚠ DO NOT CONSUME A FOCUS THIS RUN COULD NOT ACT ON — that is how "take me to that field"
    // became "here is the whole farm". `geoms` is refetched whenever the visible field set
    // changes, so the FIRST value can be a truthy object that does not yet hold this CLU. The
    // old code read that as "no boundary", zoomed nothing, and called onFocusDone anyway — the
    // target was gone for good, and the farm-wide auto-fit above was simply the last fit left
    // standing. Returning here keeps the focus alive; this effect re-runs on `geoms`, so the
    // zoom lands the moment the boundary actually arrives.
    if (key && (!geoms || !geoms[key])) return;
    if (key) {
      setFocusClu(key);
      // On a phone, "show this field" opens the field's page too, not just a zoom to it.
      if (phoneRef.current && openFeatRef.current && fc && fc.features) {
        const ft = fc.features.find((x) => cluLower(x.properties && x.properties.clu_id) === key);
        if (ft) openFeatRef.current(ft);
      }
    }
    else if (focus.lat != null && focus.lon != null && mapRef.current) {
      const map = mapRef.current;
      setTimeout(() => { try { map.setView([focus.lat, focus.lon], 15); } catch (e) {} }, 300);
    }
    onFocusDone && onFocusDone();
  }, [focus, L, geoms]);   // eslint-disable-line

  // ── Area-measure tool ──────────────────────────────────────────────────
  // Click the map to drop polygon vertices; the enclosed area is reported in
  // acres live. Geodesic (spherical-excess) area on the WGS84 sphere — the same
  // formula Leaflet.draw uses — so it's accurate at NY latitudes without pulling
  // in the whole Leaflet.draw plugin. Result in m², converted to acres below.
  const measureArea = useMemo(() => {
    if (mpts.length < 3) return 0;
    const d2r = Math.PI / 180, R = 6378137;
    let a = 0;
    for (let i = 0; i < mpts.length; i++) {
      const p1 = mpts[i], p2 = mpts[(i + 1) % mpts.length];
      a += ((p2.lng - p1.lng) * d2r) * (2 + Math.sin(p1.lat * d2r) + Math.sin(p2.lat * d2r));
    }
    return Math.abs(a * R * R / 2);   // m²
  }, [mpts]);
  const measureAcres = measureArea / 4046.8564224;

  const startMeasure = () => { setMpts([]); setMeasuring(true); };
  const stopMeasure  = () => { setMeasuring(false); setMpts([]); };

  // While measuring: crosshair cursor, capture map clicks as vertices, and make
  // the overlay pane click-through so clicking *over a field* still drops a point
  // (vector layers would otherwise swallow the click). Double-click zoom is
  // disabled so a stray dblclick doesn't zoom; finish via the Done button.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L || !measuring) return;
    const pane = map.getPane("overlayPane");
    if (pane) pane.style.pointerEvents = "none";
    if (mapEl.current) mapEl.current.style.cursor = "crosshair";
    map.doubleClickZoom.disable();
    const onClick = (e) => setMpts(prev => [...prev, e.latlng]);
    map.on("click", onClick);
    return () => {
      map.off("click", onClick);
      map.doubleClickZoom.enable();
      if (pane) pane.style.pointerEvents = "";
      if (mapEl.current) mapEl.current.style.cursor = "";
    };
  }, [measuring, L]);

  // (re)draw the measured polygon + its vertices
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (measureRef.current) { map.removeLayer(measureRef.current); measureRef.current = null; }
    if (!measuring || mpts.length === 0) return;
    const g = L.layerGroup().addTo(map);
    if (mpts.length >= 3) L.polygon(mpts, { color:"#ffd34d", weight:2.5, fillColor:"#ffd34d", fillOpacity:0.25, dashArray:"5,4" }).addTo(g);
    else if (mpts.length === 2) L.polyline(mpts, { color:"#ffd34d", weight:2.5, dashArray:"5,4" }).addTo(g);
    mpts.forEach(p => L.circleMarker(p, { radius:4, color:"#1a2e1a", weight:1.5, fillColor:"#ffd34d", fillOpacity:1 }).addTo(g));
    measureRef.current = g;
  }, [mpts, measuring, L]);

  // ── PRF rainfall-index grid overlay ──────────────────────────────────────
  // Lazy-load the grid geometry + this crop year's index rows the first time the
  // toggle is switched on. Covers ALL grids (NY + out-of-state MI/PA book grids),
  // so a grower's field lights up with the same RMA index the Grid Map shows.
  useEffect(() => {
    if (!showPrfGrids || prfData) return;
    let alive = true;
    Promise.all([
      sbFetch("prf_grid_locations", { select: "grid_id,min_lat,max_lat,min_lon,max_lon" }),
      sbFetch("prf_grid_indices", {
        select: "grid_id,interval_code,estimated_index_value,final_index_value,estimated_rainfall_inches,normal_rainfall_inches,normal_to_date_inches,estimated_as_of,is_final",
        filters: [`crop_year=eq.${CURRENT_CROP_YEAR}`],
      }),
    ]).then(([locs, idx]) => {
      if (!alive) return;
      const byGridIv = {};
      for (const r of idx || []) byGridIv[`${r.grid_id}|${r.interval_code}`] = r;
      setPrfData({ locs: locs || [], byGridIv });
    }).catch(() => { if (alive) setPrfData({ locs: [], byGridIv: {} }); });
    return () => { alive = false; };
  }, [showPrfGrids]);

  // When coloring by an interval, sum that interval's rainfall straight from the
  // daily-precip table (fresh for ALL grids). The stored estimated_index in
  // prf_grid_indices only refreshes for the ~37 book grids each sync, so the other
  // ~214 go stale (that's why an overlay cell could disagree with the Grid Map).
  // Computing from daily here — same source the Grid Map uses — keeps them equal.
  useEffect(() => {
    if (!showPrfGrids || !prfColorBy || !prfIv) { setPrfDaily(null); setPrfDailyAsOf(null); return; }
    const mr = _PRF_MR[prfIv]; if (!mr) { setPrfDaily(null); setPrfDailyAsOf(null); return; }
    let alive = true;
    const [m0, m1] = mr;
    const endY = m1 < m0 ? CURRENT_CROP_YEAR + 1 : CURRENT_CROP_YEAR;
    const s = new Date(Date.UTC(CURRENT_CROP_YEAR, m0, 1)).toISOString().slice(0, 10);
    const e = new Date(Date.UTC(endY, m1 + 1, 0)).toISOString().slice(0, 10);
    setPrfDaily(null); setPrfDailyAsOf(null);
    sbFetch("prf_grid_daily_precip", {
      select: "grid_id,precip_mm,obs_date",
      filters: [`obs_date=gte.${s}`, `obs_date=lte.${e}`],
      order: "id.asc", // STABLE order — this spans ~15k rows / multiple pages; paging
                        // without an order double-counts at page boundaries.
    }).then((rws) => {
      if (!alive) return;
      const sum = {}, cnt = {};
      let thru = null;   // the last day the daily feed actually reaches in this window
      for (const r of rws || []) {
        const g = String(r.grid_id); sum[g] = (sum[g] || 0) + (Number(r.precip_mm) || 0) / 25.4; cnt[g] = (cnt[g] || 0) + 1;
        const d = r.obs_date ? String(r.obs_date).slice(0, 10) : null;
        if (d && (!thru || d > thru)) thru = d;
      }
      // Coverage guard: only trust a grid's running rainfall if its daily data covers
      // most of the elapsed window. Grids added mid-interval (e.g. MI grids ingested
      // only in the last few days) otherwise show a false ~1% index / "would trigger".
      const todayIso = new Date().toISOString().slice(0, 10);
      const dataEnd = thru || todayIso;
      const capEnd = e < dataEnd ? e : dataEnd;
      const elapsedDays = Math.round((new Date(capEnd) - new Date(s)) / 86400000) + 1;
      const need = Math.max(1, Math.floor(elapsedDays * 0.7));
      const covered = {};
      for (const g in sum) { if (cnt[g] >= need) covered[g] = sum[g]; }
      setPrfDaily(covered); setPrfDailyAsOf(thru);
    }).catch(() => { if (alive) setPrfDaily({}); });
    return () => { alive = false; };
  }, [showPrfGrids, prfColorBy, prfIv]);

  // Is the selected interval RMA-settled (FINAL) or still a NOAA estimate? RMA
  // settles a whole interval at once, so any is_final row means the interval is final.
  const prfIvStatus = useMemo(() => {
    if (!prfData || !prfIv) return null;
    let isFinal = false, asOf = null;
    for (const k in prfData.byGridIv) {
      if (!k.endsWith("|" + prfIv)) continue;
      const r = prfData.byGridIv[k];
      if (r.is_final && r.final_index_value != null) isFinal = true;
      if (r.estimated_as_of && (!asOf || r.estimated_as_of > asOf)) asOf = r.estimated_as_of;
    }
    return { isFinal, asOf };
  }, [prfData, prfIv]);

  // Draw / redraw the PRF grid overlay. Two modes:
  //   • boundaries (default) — clean cell outlines + grid-ID labels, satellite shows through
  //   • color-by-rainfall     — fill each cell by the selected interval's index (FINAL if
  //     RMA has settled, else the running ESTIMATE). Popup always carries full detail.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (prfLayerRef.current) { map.removeLayer(prfLayerRef.current); prfLayerRef.current = null; }
    if (!showPrfGrids || !prfData) return;
    const coloring = prfColorBy && !!prfIv;
    const grp = L.layerGroup();
    for (const g of prfData.locs) {
      if (g.min_lat == null || g.min_lon == null) continue;
      const rec = coloring ? prfData.byGridIv[`${g.grid_id}|${prfIv}`] : null;
      const isFinal = rec ? !!rec.is_final : false;
      const normal = rec && rec.normal_rainfall_inches != null ? Number(rec.normal_rainfall_inches) : null;
      // Index priority: RMA FINAL (settled) → fresh daily-precip ÷ normal (all grids,
      // matches the Grid Map) → stored estimate (fallback, e.g. pre-daily intervals).
      const dailyRain = coloring && prfDaily && prfDaily[String(g.grid_id)] != null ? prfDaily[String(g.grid_id)] : null;
      // One rule for every PRF screen (src/prf/prfIntervalStatus.js): while the
      // interval is open the cell reads percent of normal TO DATE (rain so far ÷ the
      // normal for the same days), colored on the neutral to-date scale and never
      // called against a trigger. Rain so far ÷ the whole interval's normal read low
      // and flagged open grids "would trigger".
      let idxVal = null, rain = null, soFar = false;
      const ivOpen = coloring && !isFinal && PRF_IS.intervalStatus(prfIv, CURRENT_CROP_YEAR) !== "closed";
      // A closed interval whose daily rain stops short of its last day is still a
      // reading to date, never an index (same rule as the PRF Grid page).
      const ivWinFM = coloring ? PRF_IS.intervalWindow(prfIv, CURRENT_CROP_YEAR) : null;
      const ivEndFM = ivWinFM ? new Date(ivWinFM.end).toISOString().slice(0, 10) : null;
      const dailyShort = !isFinal && dailyRain != null && !!ivEndFM && !(prfDailyAsOf && prfDailyAsOf >= ivEndFM);
      if (!coloring) { /* boundaries only */ }
      else if (isFinal && rec && rec.final_index_value != null) { idxVal = Number(rec.final_index_value); rain = rec.estimated_rainfall_inches != null ? Number(rec.estimated_rainfall_inches) : null; }
      else if (ivOpen || dailyShort) {
        soFar = true;
        if (dailyRain != null && normal > 0) {
          // Normal for the SAME days the rain covers: through the feed's last day,
          // not through yesterday (the feed runs a day or two behind).
          const nSoFar = prfDailyAsOf ? PRF_IS.normalToDate(prfIv, CURRENT_CROP_YEAR, { normalInches: normal, asOf: prfDailyAsOf }) : null;
          rain = dailyRain; idxVal = nSoFar > 0 ? dailyRain / nSoFar * 100 : null;
        } else if (rec) {
          idxVal = PRF_IS.pctOfNormalToDate(prfIv, CURRENT_CROP_YEAR, { estimatedInches: rec.estimated_rainfall_inches, normalInches: rec.normal_rainfall_inches,
            normalToDateInches: rec.normal_to_date_inches, asOf: rec.estimated_as_of });
          rain = rec.estimated_rainfall_inches != null ? Number(rec.estimated_rainfall_inches) : null;
        }
      }
      else if (dailyRain != null && normal > 0) { rain = dailyRain; idxVal = dailyRain / normal * 100; }
      else if (rec && rec.estimated_index_value != null) { idxVal = Number(rec.estimated_index_value); rain = rec.estimated_rainfall_inches != null ? Number(rec.estimated_rainfall_inches) : null; }
      const rect = L.rectangle([[g.min_lat, g.min_lon], [g.max_lat, g.max_lon]],
        coloring
          ? { color: "#20361f", weight: 0.7, opacity: 0.6, fillColor: soFar ? prfToDateColor(idxVal) : prfIndexColor(idxVal), fillOpacity: 0.42 }
          : { color: "#22d3ee", weight: 1.3, opacity: 0.95, fillColor: "#22d3ee", fillOpacity: 0.06 });
      rect.bindPopup(
        `<div style="font-size:12px;line-height:1.5"><b>Grid ${g.grid_id}</b>` +
        (coloring ? ` · ${_PRF_LABEL[prfIv] || prfIv}` : "") + `<br/>` +
        (coloring
          ? (idxVal != null
              ? (soFar ? `<b>${idxVal.toFixed(0)}%</b> of normal so far <span style="color:#5b6f8a;font-weight:700">interval open</span><br/>`
                       : `Index <b>${idxVal.toFixed(0)}%</b> of normal ${isFinal ? `<span style="color:#2d6a2d;font-weight:700">RMA FINAL</span>` : `<span style="color:#7a5a20;font-weight:700">est. at close</span>`}<br/>`)
              : `<span style="color:#888">no index yet</span><br/>`) +
            (normal != null ? `Interval normal <b>${normal.toFixed(2)}"</b>` : "") +
            (rain != null ? ` · received ${rain.toFixed(2)}"` : "") +
            (!soFar && idxVal != null && idxVal < 90 ? `<br/><span style="color:#b5321f;font-weight:700">below 90% — would trigger at 90% coverage</span>` : "")
          : `<span style="color:#888">Toggle “Color by rainfall” to see the index</span>`) +
        `</div>`
      );
      grp.addLayer(rect);
      // grid-ID label pinned at the cell center (non-interactive so clicks hit the rect)
      const clat = (Number(g.min_lat) + Number(g.max_lat)) / 2;
      const clon = (Number(g.min_lon) + Number(g.max_lon)) / 2;
      grp.addLayer(L.marker([clat, clon], {
        interactive: false, keyboard: false,
        icon: L.divIcon({
          className: "prf-grid-label",
          html: `<span style="font-size:10px;font-weight:700;color:#fff;text-shadow:0 0 3px #000,0 0 3px #000,0 0 3px #000;white-space:nowrap">${g.grid_id}</span>`,
          iconSize: [44, 12], iconAnchor: [22, 6],
        }),
      }));
    }
    grp.addTo(map);
    // rectangles under the field polygons; the label markers live in markerPane (above)
    grp.eachLayer((lyr) => { if (lyr instanceof L.Rectangle) { try { lyr.bringToBack(); } catch (e) {} } });
    prfLayerRef.current = grp;
    return () => { if (prfLayerRef.current) { try { map.removeLayer(prfLayerRef.current); } catch (e) {} prfLayerRef.current = null; } };
  }, [showPrfGrids, prfData, prfIv, prfColorBy, prfDaily, prfDailyAsOf, L]);

  // Farmer's own field boundaries (imported from their JD/FieldView export) — loaded on
  // demand when the overlay is toggled on. A new import bumps `labels`, which clears the
  // cache so the overlay reflects the latest boundaries next time it's shown.
  useEffect(() => { setFarmerBounds(null); }, [labels]);
  useEffect(() => {
    if (!showFarmerBounds || farmerBounds) return;
    let alive = true;
    sbFetch("farmer_field_boundaries", { select: "farmer_id,field_name,geometry" })
      .then(d => { if (alive) setFarmerBounds(d || []); })
      .catch(() => { if (alive) setFarmerBounds([]); });
    return () => { alive = false; };
  }, [showFarmerBounds, farmerBounds]);
  // Draw the farmer boundaries ON TOP of the CLU field polygons — a distinct magenta
  // dashed outline (≠ white CLU borders, ≠ yellow county lines, ≠ cyan PRF grids), with
  // the farmer's own field name as the tooltip. Scoped to the farms currently in view.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (farmerBoundsRef.current) { try { map.removeLayer(farmerBoundsRef.current); } catch (e) {} farmerBoundsRef.current = null; }
    if (!showFarmerBounds || !farmerBounds || !farmerBounds.length) return;
    const visIds = new Set((rows || []).map(r => r.farmer_id).filter(Boolean));
    const selIds = selSet.size ? selSet : null;   // honor the tree selection when any farm is picked
    const feats = farmerBounds
      .filter(b => b.geometry && visIds.has(b.farmer_id) && (!selIds || selIds.has(b.farmer_id)))
      .map(b => ({ type: "Feature", geometry: b.geometry, properties: { name: b.field_name } }));
    if (!feats.length) return;
    const layer = L.geoJSON({ type: "FeatureCollection", features: feats }, {
      style: { color: "#ff3db4", weight: 2, opacity: 0.95, fill: false, dashArray: "6,3" },
      onEachFeature: (f, lyr) => { if (f.properties.name) lyr.bindTooltip(`${f.properties.name}`, { sticky: true, direction: "top" }); },
    }).addTo(map);
    try { layer.bringToFront(); } catch (e) {}
    farmerBoundsRef.current = layer;
    return () => { if (farmerBoundsRef.current) { try { map.removeLayer(farmerBoundsRef.current); } catch (e) {} farmerBoundsRef.current = null; } };
  }, [showFarmerBounds, farmerBounds, rows, selSet, L]);

  // Landowner overlay — LIVE per-viewport tax-parcel query. Nothing is stored:
  // when the toggle is on we query /api/market?fn=parcels for JUST the parcels in
  // the current map bounds, and re-query (debounced) whenever the map settles.
  // Gated to zoom ≥ 15 so we never ask the NYS service (~3.8M parcels statewide)
  // for a region-sized bbox — that keeps every request small and the map snappy.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (!showParcels) { setParcels(null); return; }
    let alive = true, timer = null;
    const run = () => {
      if (!alive || !mapRef.current) return;
      if (map.getZoom() < 15) { setParcels({ status: "zoom", features: [] }); return; }
      const b = map.getBounds();
      const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].map(x => x.toFixed(6)).join(",");
      setParcels(p => ({ status: "loading", features: (p && p.features) || [] }));
      fetch(`/api/market?fn=parcels&bbox=${bbox}`)
        .then(r => r.json())
        .then(gj => {
          if (!alive) return;
          if (gj && gj.tooWide) { setParcels({ status: "zoom", features: [] }); return; }
          if (!gj || gj.error) { setParcels({ status: "error", features: [] }); return; }
          setParcels({ status: "ok", features: gj.features || [] });
        })
        .catch(() => { if (alive) setParcels({ status: "error", features: [] }); });
    };
    const debounced = () => { if (timer) clearTimeout(timer); timer = setTimeout(run, 400); };
    run();                       // initial load for the current view
    map.on("moveend", debounced); // fires on pan AND zoom
    return () => { alive = false; if (timer) clearTimeout(timer); try { map.off("moveend", debounced); } catch (e) {} };
  }, [showParcels, L]);

  // Draw the viewport's parcels UNDER the CLU fields (bringToBack) — an amber
  // outline with a faint fill. Sitting below the crop polygons means a click
  // inside an insured field still selects the field; a click on open parcel land
  // opens the owner popup. Popup = owner + mailing address + assessed value + acres.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (parcelsRef.current) { try { map.removeLayer(parcelsRef.current); } catch (e) {} parcelsRef.current = null; }
    if (!showParcels || !parcels || !parcels.features || !parcels.features.length) return;
    const esc = (v) => String(v == null ? "" : v).replace(/[<>&]/g, c => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
    const money = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? "$" + n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"; };
    const layer = L.geoJSON({ type: "FeatureCollection", features: parcels.features }, {
      // Blue outline — deliberately OUTSIDE the crop palette (corn #ffd21f, wheat,
      // oats… are all warm gold/amber) so parcel lines never read as a crop field.
      // Also distinct from PRF cyan (#22d3ee) and farmer-boundary magenta.
      style: { color: "#2563eb", weight: 1, opacity: 0.95, fillColor: "#2563eb", fillOpacity: 0.05 },
      onEachFeature: (f, lyr) => {
        const p = f.properties || {};
        const owner = esc(p.PRIMARY_OWNER || "—");
        const addOwner = p.ADD_OWNER ? `<div style="color:#5a6a5a">& ${esc(p.ADD_OWNER)}</div>` : "";
        const mail = [p.MAIL_ADDR, [p.MAIL_CITY, p.MAIL_STATE].filter(Boolean).join(", "), p.MAIL_ZIP].filter(Boolean).join("<br>");
        const acres = Number(p.CALC_ACRES || p.ACRES);
        // Property (parcel) address — the only location Orleans provides (no mailing).
        const loc = p.PARCEL_ADDR ? `<div style="margin-top:5px;color:#5a6a5a">📍 ${esc(p.PARCEL_ADDR)}</div>` : "";
        // Footer credits the source when it's a county service (Monroe/Niagara/Orleans).
        const src = p.COUNTY_SRC ? ` · ${esc(p.COUNTY_SRC)}` : "";
        const html =
          `<div style="font:12px/1.45 system-ui;min-width:170px">
             <div style="font-weight:800;color:#1f3320;font-size:13px">${owner}</div>${addOwner}
             ${mail ? `<div style="margin-top:5px;color:#5a6a5a">📮 ${esc(mail).replace(/,/g, ",")}</div>` : ""}
             ${loc}
             <div style="margin-top:6px;display:flex;justify-content:space-between;gap:12px;color:#3a4a3a">
               <span>Assessed</span><span style="font-weight:700">${money(p.TOTAL_AV)}</span></div>
             <div style="display:flex;justify-content:space-between;gap:12px;color:#3a4a3a">
               <span>Full mkt</span><span style="font-weight:700">${money(p.FULL_MARKET_VAL)}</span></div>
             <div style="display:flex;justify-content:space-between;gap:12px;color:#3a4a3a">
               <span>Acres</span><span style="font-weight:700">${Number.isFinite(acres) && acres > 0 ? acres.toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—"}</span></div>
             ${p.PRINT_KEY ? `<div style="margin-top:5px;font-size:10px;color:#677167">SBL ${esc(p.PRINT_KEY)}${p.PROP_CLASS ? ` · class ${esc(p.PROP_CLASS)}` : ""}${src}</div>` : (src ? `<div style="margin-top:5px;font-size:10px;color:#677167">${esc(p.COUNTY_SRC)}</div>` : "")}
           </div>`;
        // autoPan:false — a popup opening near the edge would otherwise pan the map,
        // which fires moveend → re-query → the layer rebuilds and the popup vanishes.
        // That pan is unavoidable on mobile (small map), so just don't pan.
        lyr.bindPopup(html, { autoPan: false });
        lyr.on("mouseover", () => { try { lyr.setStyle({ weight: 2.5, fillOpacity: 0.14 }); } catch (e) {} });
        lyr.on("mouseout", () => { try { lyr.setStyle({ weight: 1, fillOpacity: 0.06 }); } catch (e) {} });
      },
    }).addTo(map);
    try { layer.bringToBack(); } catch (e) {}
    parcelsRef.current = layer;
    return () => { if (parcelsRef.current) { try { map.removeLayer(parcelsRef.current); } catch (e) {} parcelsRef.current = null; } };
  }, [showParcels, parcels, L]);

  // ── RADAR ────────────────────────────────────────────────────────────────
  // Region-wide, and continuous from the recent past through +18h on ONE
  // 15-minute timeline. Frames come from two sources and are always labelled as
  // such in the scrubber — observed radar is measured, HRRR is a model, and
  // blurring that distinction is the fastest way to make the layer untrustworthy.
  //
  // Both sources are fetched as Web-Mercator PNGs for the SAME snapped bbox, so
  // they drop onto one L.imageOverlay and scrubbing across "now" is seamless.
  // See api/_radar.js for why Mercator (not lat/lon) matters here.

  // Bbox snapping and frame-URL construction now live at module scope
  // (radarSnapBbox / radarFrameUrl) because the Weather page draws the same frames.
  // Those two are where a second copy would drift first — the observed source is
  // a WMS in a specific projection and the forecast source is our own endpoint
  // with an ensemble floor, and getting either subtly wrong still renders
  // something plausible.

  // Manifest + bbox, refreshed when the layer opens and (debounced) when the map
  // settles somewhere new. The manifest is the ONLY source of frame times — the
  // client never invents one, so the timeline can't drift from what's servable.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (!showRadar) {
      setRadarMan(null); setRadarBbox(null); setRadarPlay(false);
      setRadarAgree(null); setRadarReady(0); radarCacheRef.current = new Map();
      return;
    }
    let alive = true, timer = null;
    const run = () => {
      if (!alive || !mapRef.current) return;
      const bbox = radarSnapBbox(mapRef.current);
      setRadarBbox((prev) => (prev && prev.join(",") === bbox.join(",") ? prev : bbox));
      fetch("/api/market?fn=radar&op=manifest")
        .then((r) => r.json())
        .then((j) => {
          if (!alive || !j || !j.ok || !Array.isArray(j.frames) || !j.frames.length) return;
          setRadarMan(j);
          // Open on "now" — the present is the default view, forward is the point.
          const nowIdx = j.frames.findIndex((f) => f.isNow);
          setRadarIdx(nowIdx >= 0 ? nowIdx : 0);
        })
        .catch(() => {});
    };
    const debounced = () => { if (timer) clearTimeout(timer); timer = setTimeout(run, 500); };
    run();
    map.on("moveend", debounced);
    return () => { alive = false; if (timer) clearTimeout(timer); try { map.off("moveend", debounced); } catch (e) {} };
  }, [showRadar, L, radarSnapBbox, radarTick]);

  // Keep the timeline live. Without this the manifest is fetched once when the
  // layer opens and never again: leave the tab up through lunch and "now" is
  // silently an hour in the past while the map still presents it as the present.
  // A radar that quietly ages is worse than no radar, because it still looks right.
  useEffect(() => {
    if (!showRadar) return;
    const id = setInterval(() => setRadarTick((t) => t + 1), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [showRadar]);

  // Ensemble spread — the measured agreement radius per lead. Only fetched in
  // ensemble mode, because it costs several GRIB reads per sampled lead.
  useEffect(() => {
    if (!showRadar || radarMode !== "prob" || !radarBbox) { setRadarSpread(null); return; }
    let alive = true;
    fetch(`/api/market?fn=radar&op=spread&bbox=${radarBbox.join(",")}`)
      .then((r) => r.json())
      .then((j) => {
        if (!alive || !j || !j.ok) return;
        const m = {};
        (j.leads || []).forEach((l) => { if (l.radiusKm != null) m[l.leadMin] = l; });
        setRadarSpread(m);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [showRadar, radarMode, radarBbox, radarTick]);

  // Preload the FORECAST frames into the browser cache before animating. Without
  // this, play() swaps to a URL that hasn't downloaded and the overlay blanks
  // between frames — the single thing that makes a radar loop look broken.
  //
  // Observed frames are deliberately NOT preloaded: they are now WMS tiles, so
  // there is no one URL to warm, and Leaflet holds the previous frame's tiles
  // painted until the next frame's arrive (keepBuffer). Counting them here would
  // also mean counting requests we never issue, which stalled the play button at
  // "caching 21/37" forever.
  const fcFrameCount = useMemo(
    () => (radarMan ? radarMan.frames.filter((f) => f.kind === "forecast").length : 0),
    [radarMan]);

  useEffect(() => {
    if (!showRadar || !radarMan || !radarBbox) return;
    let alive = true;
    const W = 1000, H = 620;
    setRadarReady(0);
    const cache = new Map();
    radarCacheRef.current = cache;
    let done = 0;
    // Nearest-first: the frames next to "now" are the ones being looked at, so
    // load outward from there rather than front-to-back.
    const nowIdx = Math.max(0, radarMan.frames.findIndex((f) => f.isNow));
    const order = radarMan.frames
      .map((fr, i) => ({ fr, i }))
      .filter(({ fr }) => fr.kind === "forecast")
      .sort((a, b) => Math.abs(a.i - nowIdx) - Math.abs(b.i - nowIdx));
    order.forEach(({ fr }) => {
      const url = radarFrameUrl(fr, radarBbox, W, H, radarMode);
      if (!url) return;
      const img = new Image();
      const tick = () => { if (!alive) return; done++; setRadarReady(done); };
      img.onload = tick;
      img.onerror = tick;      // a missing frame must not stall the counter
      img.src = url;
      cache.set(fr.t, url);
    });
    return () => { alive = false; };
  }, [showRadar, radarMan, radarBbox, radarMode]);

  // The overlay itself — see drawRadarFrame at module scope. Observed frames go out
  // as WMS TILES (correct resolution at every zoom, square pixels, no stretching);
  // forecast frames stay a capped, pixelated imageOverlay. Both live in a pane
  // between the satellite basemap and the field polygons, so fields stay visible
  // and clickable through the weather.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (!showRadar || !radarMan) { clearRadarFrame(map, radarLayerRef); return; }
    const fr = radarMan.frames[Math.min(radarIdx, radarMan.frames.length - 1)];
    // A forecast frame needs the bbox it was rendered for; an observed frame does
    // not, so radar still draws while a fresh bbox is being settled.
    if (fr && fr.kind === "forecast" && !radarBbox) return;
    drawRadarFrame(L, map, radarLayerRef, fr, radarBbox, radarOpacity, radarMode);
  }, [showRadar, radarMan, radarIdx, radarBbox, radarOpacity, L, radarMode]);

  // Tear the overlay down when the layer is switched off or the panel unmounts.
  useEffect(() => () => { clearRadarFrame(mapRef.current, radarLayerRef); }, []);

  // Playback. Holds a beat on the last frame so the loop reads as an ending
  // rather than a hard snap back to the start.
  useEffect(() => {
    if (!radarPlay || !radarMan || !radarMan.frames.length) return;
    const last = radarMan.frames.length - 1;
    const delay = radarIdx === last ? 900 : 320;
    const t = setTimeout(() => setRadarIdx((i) => (i >= last ? 0 : i + 1)), delay);
    return () => clearTimeout(t);
  }, [radarPlay, radarIdx, radarMan]);

  // ── FORECAST TOTALS ──────────────────────────────────────────────────────
  // Which windows a run can currently reach (HRRR publishes forecast hours
  // progressively, so +48h is not always available). The backend answers with
  // the REAL covered period; the UI prints those times rather than implying the
  // window starts at this exact minute.
  useEffect(() => {
    if (!showPrecip) { setPrecipWins(null); return; }
    let alive = true;
    fetch("/api/market?fn=precip&op=windows")
      .then((r) => r.json())
      .then((j) => {
        if (!alive || !j || !j.ok) return;
        setPrecipWins(j);
        // Fall back to a window that actually exists right now.
        const have = (j.windows || []).map((w) => w.hours);
        if (have.length && !have.includes(precipHours)) setPrecipHours(have[have.length - 1]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [showPrecip, radarTick]);   // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    const drop = () => {
      if (precipLayerRef.current) { try { map.removeLayer(precipLayerRef.current); } catch (e) {} precipLayerRef.current = null; }
    };
    if (!showPrecip || !radarBbox) { drop(); return; }
    if (!map.getPane("radarPane")) {
      const pane = map.createPane("radarPane");
      pane.style.zIndex = 350;
      pane.style.pointerEvents = "none";
    }
    const url = `/api/market?fn=precip&op=frame&hours=${precipHours}&field=${precipField}`
      + `&bbox=${radarBbox.join(",")}&w=1000&h=620`;
    const bounds = [[radarBbox[1], radarBbox[0]], [radarBbox[3], radarBbox[2]]];
    if (!precipLayerRef.current) {
      precipLayerRef.current = L.imageOverlay(url, bounds, {
        opacity: radarOpacity, pane: "radarPane", interactive: false,
        className: "silas-radar-frame",
      }).addTo(map);
    } else {
      try {
        precipLayerRef.current.setBounds(L.latLngBounds(bounds));
        precipLayerRef.current.setUrl(url);
        precipLayerRef.current.setOpacity(radarOpacity);
      } catch (e) {}
    }
    return drop;
  }, [showPrecip, precipHours, precipField, radarBbox, radarOpacity, L]);

  // Values grid for the current window — one fetch, then every probe is local.
  useEffect(() => {
    if (!showPrecip || !radarBbox) { setPrecipGrid(null); setPrecipProbe(null); setPrecipHover(null); return; }
    let alive = true;
    setPrecipGrid(null);
    fetch(`/api/market?fn=precip&op=grid&hours=${precipHours}&field=${precipField}&bbox=${radarBbox.join(",")}`)
      .then((r) => r.json())
      .then((j) => { if (alive && j && j.ok) setPrecipGrid(j); })
      .catch(() => {});
    return () => { alive = false; };
  }, [showPrecip, precipHours, precipField, radarBbox]);

  // Sample the grid at a lat/lon. Uses the SAME Mercator row mapping the server
  // rendered with, so the number returned is the cell that was actually painted
  // under that point rather than a neighbour.
  // Returns { lo, mid, hi } in inches — the spread across recent runs, not one
  // number. A single figure for rainfall reads as a promise; 0.10" and
  // "0.04 to 0.95" are completely different things to plan around.
  const precipAt = useCallback((lat, lon) => {
    const g = precipGrid;
    if (!g || !g.mid) return null;
    const [w, s, e, n] = g.bbox;
    if (lat < s || lat > n || lon < w || lon > e) return null;
    const D = Math.PI / 180;
    const mY = (la) => Math.log(Math.tan(Math.PI / 4 + (la * D) / 2));
    const yT = mY(n), yB = mY(s);
    const px = Math.floor(((lon - w) / (e - w)) * g.nx);
    const py = Math.floor(((yT - mY(lat)) / (yT - yB)) * g.ny);
    if (px < 0 || py < 0 || px >= g.nx || py >= g.ny) return null;
    const c = py * g.nx + px;
    return { lo: g.lo[c] / g.scale, mid: g.mid[c] / g.scale, hi: g.hi[c] / g.scale };
  }, [precipGrid]);

  // Tap (works everywhere) sets a sticky probe; hover (desktop only) updates a
  // live value. Mobile gets the identical readout because the sticky probe, not
  // the cursor, is what drives it.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L || !showPrecip) return;
    // Tap PINS a reading; hover (desktop only) previews into the SAME display
    // instead of adding a second number somewhere else on the bar. One value in
    // one place — the previous layout had the tapped figure, an "under cursor"
    // figure and a "max in view" figure all competing.
    const onClick = (ev) => {
      const { lat, lng } = ev.latlng;
      const v = precipAt(lat, lng);
      setPrecipProbe(v == null ? null : { lat, lon: lng, ...v, pinned: true });
    };
    const onMove = (ev) => {
      if (isMobile) return;
      const v = precipAt(ev.latlng.lat, ev.latlng.lng);
      setPrecipHover(v);
    };
    const onOut = () => setPrecipHover(null);
    map.on("click", onClick);
    map.on("mousemove", onMove);
    map.on("mouseout", onOut);
    return () => {
      try { map.off("click", onClick); map.off("mousemove", onMove); map.off("mouseout", onOut); } catch (e) {}
    };
  }, [showPrecip, L, precipAt, isMobile]);

  // Arrival grid for the current view — one fetch, then every tap is local.
  useEffect(() => {
    if (!showRadar || !radarBbox) { setRadarArrival(null); setRadarProbe(null); return; }
    let alive = true;
    fetch(`/api/market?fn=radar&op=arrival&bbox=${radarBbox.join(",")}&hours=12`)
      .then((r) => r.json())
      .then((j) => { if (alive && j && j.ok) setRadarArrival(j); })
      .catch(() => {});
    return () => { alive = false; };
  }, [showRadar, radarBbox, radarTick]);

  // Tap on the radar layer → when does it get here, and how much do the runs
  // disagree. Reports the SPREAD across members, never a single instant.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L || !showRadar) return;
    const onClick = (ev) => {
      const a = radarArrival;
      if (!a) return;
      const { lat, lng } = ev.latlng;
      const [w, s, e, n] = a.bbox;
      if (lat < s || lat > n || lng < w || lng > e) { setRadarProbe(null); return; }
      const D = Math.PI / 180;
      const mY = (la) => Math.log(Math.tan(Math.PI / 4 + (la * D) / 2));
      const yT = mY(n), yB = mY(s);
      const px = Math.floor(((lng - w) / (e - w)) * a.nx);
      const py = Math.floor(((yT - mY(lat)) / (yT - yB)) * a.ny);
      if (px < 0 || py < 0 || px >= a.nx || py >= a.ny) { setRadarProbe(null); return; }
      const c = py * a.nx + px;
      const hits = a.arrivals.map((arr) => arr[c]).filter((t) => t > 0);
      setRadarProbe({
        lat, lon: lng, total: a.arrivals.length, runs: hits.length,
        firstH: hits.length ? Math.min(...hits) : null,
        lastH: hits.length ? Math.max(...hits) : null,
      });
    };
    map.on("click", onClick);
    return () => { try { map.off("click", onClick); } catch (e) {} };
  }, [showRadar, L, radarArrival]);

  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (precipMarkRef.current) { try { map.removeLayer(precipMarkRef.current); } catch (e) {} precipMarkRef.current = null; }
    if (!showRadar || !radarProbe) return;
    precipMarkRef.current = L.circleMarker([radarProbe.lat, radarProbe.lon], {
      radius: 6, color: "#fff", weight: 2.5, fillColor: "#1f3320", fillOpacity: 0.85, interactive: false,
    }).addTo(map);
  }, [showRadar, radarProbe, L]);

  // Pull the day-by-day forecast for whatever point is pinned. Only on a PINNED
  // probe, never on hover — it is ~20 GRIB reads and hovering would fire it
  // continuously across the map.
  useEffect(() => {
    if (!showPrecip || !precipProbe || !precipProbe.pinned) { setPrecipDaily(null); return; }
    let alive = true;
    setPrecipDailyBusy(true);
    setPrecipDaily(null);
    setWatchName(null); setWatchSaved(false);       // a new point is a new save
    fetch(`/api/market?fn=precip&op=daily&lat=${precipProbe.lat.toFixed(3)}&lon=${precipProbe.lon.toFixed(3)}&days=4`)
      .then((r) => r.json())
      .then((j) => { if (alive && j && j.ok) setPrecipDaily(j); })
      .catch(() => {})
      .finally(() => { if (alive) setPrecipDailyBusy(false); });
    return () => { alive = false; };
  }, [showPrecip, precipProbe]);

  // Crosshair at the probed point so the number has a visible anchor.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (precipMarkRef.current) { try { map.removeLayer(precipMarkRef.current); } catch (e) {} precipMarkRef.current = null; }
    if (!showPrecip || !precipProbe) return;
    precipMarkRef.current = L.circleMarker([precipProbe.lat, precipProbe.lon], {
      radius: 6, color: "#fff", weight: 2.5, fillColor: "#1f3320", fillOpacity: 0.85, interactive: false,
    }).addTo(map);
  }, [showPrecip, precipProbe, L]);

  // The totals layer needs the same snapped bbox the radar layer computes, so it
  // borrows that plumbing — but radar itself may be off, and then nothing would
  // ever set the bbox. Keep it fed whenever either layer is on.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L || !showPrecip) return;
    let timer = null;
    const run = () => {
      if (!mapRef.current) return;
      const bbox = radarSnapBbox(mapRef.current);
      setRadarBbox((prev) => (prev && prev.join(",") === bbox.join(",") ? prev : bbox));
    };
    const debounced = () => { if (timer) clearTimeout(timer); timer = setTimeout(run, 500); };
    run();
    map.on("moveend", debounced);
    return () => { if (timer) clearTimeout(timer); try { map.off("moveend", debounced); } catch (e) {} };
  }, [showPrecip, L, radarSnapBbox]);

  // Run-to-run agreement — the honest confidence number. Deliberately NOT a
  // "chance of rain": it measures whether consecutive HRRR runs still put the
  // echo in the same place at the same valid time. Fetched once per bbox.
  useEffect(() => {
    if (!showRadar || !radarBbox) { setRadarAgree(null); return; }
    let alive = true;
    fetch(`/api/market?fn=radar&op=agreement&bbox=${radarBbox.join(",")}&hours=18`)
      .then((r) => r.json())
      .then((j) => {
        if (!alive || !j || !j.ok) return;
        const m = {};
        (j.frames || []).forEach((f) => { if (f.agreement != null) m[f.leadMin] = f.agreement; });
        setRadarAgree(m);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [showRadar, radarBbox]);

  // Load John Deere field boundaries + per-field planting/harvest history. When a
  // SINGLE farm is selected, scope to it (farmer=) so the endpoint can also emit
  // that farm's CUSTOM-HARVESTED fields — yield on their CLUs that another
  // operator's combine ran (e.g. Silsby combined Ranney's wheat). Book-wide when
  // 0/many are selected. Refetches on selection change. Admin-gated → authedFetch.
  useEffect(() => {
    // Agent-only, same reason as the compare-operation roster below: JD field
    // boundaries and their names belong to whoever connected the account.
    if (isPortal || !showJdBounds) return;
    const only = selSet.size === 1 ? [...selSet][0] : null;
    // In the portal (and in admin preview, where the session is the ADMIN's and
    // would otherwise answer book-wide) send the caller's granted farm ids so the
    // layer shows their fields and no one else's. The server intersects whatever
    // is asked for with what the caller actually holds, so this is a fidelity fix
    // for preview — not the access control.
    const scopeIds = (isPortal || restrictFarmerIds) ? (restrictFarmerIds || []) : null;
    const scopeQ = only ? `&farmer=${only}`
                 : (scopeIds && scopeIds.length) ? `&farmers=${scopeIds.join(",")}` : "";
    // Sequence the fetch so a pending CLU→JD match (the popup's "Yield map"
    // button, which also narrows the scope) can tell whether jdFields is the
    // answer to ITS request or the leftovers of the previous scope. State can't
    // carry that — it hasn't re-rendered yet at the moment the fetch starts.
    const seq = ++jdFetchRef.current;
    const land = (gj) => { setJdFields(gj); jdFieldsSeqRef.current = seq; };
    setJdBusy(true); setJdFields(null); setJdSel(null); setJdOp(null);
    authedFetch(`/api/market?fn=jd-fieldmap${scopeQ}`)
      .then(r => r.json())
      .then(gj => land(gj && gj.features ? gj : { type: "FeatureCollection", features: [] }))
      .catch(() => land({ type: "FeatureCollection", features: [] }))
      .finally(() => setJdBusy(false));
  }, [showJdBounds, selSet]); // eslint-disable-line

  // ── Soil sampling events (metadata) ──────────────────────────────────────
  // Events are SMALL — one row per sampling, no geometry — so they load for the
  // whole book and the picker can answer "which fields have soil data" WITHOUT a
  // farm being selected first. Requiring a selection made an empty layer read as
  // broken. The heavy part (zones, with polygons) loads per selected event below,
  // which is what actually keeps this off the payload path that froze the map.
  useEffect(() => {
    // Loaded whether or not the soil layer is ON, because the layer PICKER has to be
    // able to list the samplings before one is chosen. Metadata only — no geometry —
    // so this is a small query, and it is agent-only for the same reason the layer is.
    // PARKED (see SOIL_SAMPLES_PARKED). This is the single gate for the whole map
    // surface: the layer picker's optgroup, both of its hints, the analyte legend,
    // the zone readout and the field popup's sampling picker every one of them
    // renders off `soilEvents`, so nothing downstream needs its own guard.
    if (SOIL_SAMPLES_PARKED || isPortal) { setSoilEvents(null); setSoilSel(null); setSoilEventId(null); return; }
    let alive = true;
    const ids = restrictFarmerIds ? restrictFarmerIds : (selSet.size ? [...selSet] : null);
    setSoilBusy(true);
    (async () => {
      try {
        const evs = await sbFetch("soil_sample_events", {
          select: "id,farmer_id,clu_id,field_name,sampled_on,lab,retailer,technique,grid_acres,depth_in,extraction,buffer_method,sample_count,acres,source_kind,crs_confirmed,centroid_lat,centroid_lon",
          filters: ids && ids.length ? [`farmer_id=in.(${ids.join(",")})`] : [],
          order: "sampled_on.desc",
        });
        if (!alive) return;
        setSoilEvents((evs || []).map(e => ({ ...e, samples: null })));   // samples: null = not loaded yet
        // Drop a selection that the new scope no longer contains, but never auto-pick:
        // each sampling is its own entry in the layer picker now, so choosing one is
        // an explicit act.
        setSoilEventId(prev => (prev && evs && evs.some(e => e.id === prev)) ? prev : null);
      } catch (e) { if (alive) setSoilEvents([]); }
      finally { if (alive) setSoilBusy(false); }
    })();
    return () => { alive = false; };
  }, [isPortal, selSet, restrictFarmerIds]); // eslint-disable-line

  // ── SOIL TRACKER ─────────────────────────────────────────────────────────

  // Zones for the SELECTED event only — the geometry-bearing half, fetched on demand
  // and cached onto the event so flipping between years doesn't refetch.
  useEffect(() => {
    if (!showSoil || !soilEventId || !soilEvents) return;
    const ev = soilEvents.find(e => e.id === soilEventId);
    if (!ev || ev.samples) return;                        // already loaded (or loading)
    let alive = true;
    setSoilBusy(true);
    (async () => {
      try {
        const rows = await sbFetch("soil_samples", {
          select: "id,event_id,sample_ref,lab_ref,geometry,centroid_lat,centroid_lon,acres,results",
          filters: [`event_id=eq.${soilEventId}`],
        });
        if (!alive) return;
        setSoilEvents(list => (list || []).map(e => e.id === soilEventId ? { ...e, samples: rows || [] } : e));
      } catch (e) {
        if (alive) setSoilEvents(list => (list || []).map(x => x.id === soilEventId ? { ...x, samples: [] } : x));
      } finally { if (alive) setSoilBusy(false); }
    })();
    return () => { alive = false; };
  }, [showSoil, soilEventId, soilEvents]); // eslint-disable-line

  // Draw the selected sampling event's zones, coloured by the chosen analyte.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (soilLayerRef.current) { try { map.removeLayer(soilLayerRef.current); } catch (e) {} soilLayerRef.current = null; }
    if (!showSoil || !soilEvents || !soilEventId) return;
    const ev = soilEvents.find(e => e.id === soilEventId); if (!ev) return;
    const zones = (ev.samples || []).filter(s => s.geometry);
    if (!zones.length) return;
    const def = soilAnalyteDef(soilAnalyte);
    // Scale from THIS event's own spread (2–98%), so a field reads against itself.
    const vals = zones.map(s => soilVal(s, soilAnalyte)).filter(Boolean).map(x => x.v).sort((a, b) => a - b);
    if (!vals.length) return;
    const lo = def.kind === "band" ? def.lo : vals[Math.floor(vals.length * 0.02)];
    const hi = def.kind === "band" ? def.hi : vals[Math.min(vals.length - 1, Math.floor(vals.length * 0.98))];
    const fc = {
      type: "FeatureCollection",
      features: zones.map(s => ({ type: "Feature", geometry: s.geometry, properties: { id: s.id, ref: s.sample_ref } })),
    };
    const byId = new Map(zones.map(s => [s.id, s]));
    const layer = L.geoJSON(fc, {
      style: (f) => {
        const s = byId.get(f.properties.id); const r = soilVal(s, soilAnalyte);
        const on = soilSel && soilSel.id === f.properties.id;
        return {
          color: on ? "#ffe600" : "#ffffff", weight: on ? 3 : 0.8, opacity: on ? 1 : 0.75,
          fill: true, fillColor: soilColor(r ? r.v : null, lo, hi, def.kind), fillOpacity: r ? 0.78 : 0.28,
        };
      },
      onEachFeature: (f, lyr) => {
        const s = byId.get(f.properties.id);
        lyr.on("click", (e) => { try { L.DomEvent.stopPropagation(e); } catch (_) {} setSoilSel(s); });
        const r = soilVal(s, soilAnalyte);
        lyr.bindTooltip(
          `<b>${String(f.properties.ref || "").replace(/[<>&]/g, "")}</b> · ${def.label} ` +
          (r ? `${r.v.toFixed(def.dp)}${r.unit && r.unit !== "none" ? " " + r.unit : ""}` : "not tested"),
          { sticky: true, direction: "top", opacity: 0.94 }
        );
      },
    }).addTo(map);
    soilLayerRef.current = layer;
    return () => { try { map.removeLayer(layer); } catch (e) {} };
  }, [showSoil, soilEvents, soilEventId, soilAnalyte, soilSel, L]); // eslint-disable-line

  // JD field-boundary layer — John-Deere-green outlines (distinct from white CLU
  // borders / magenta farmer / blue parcels); the selected field turns yellow.
  // Click a field → opens the year picker panel. Hover tooltip shows latest yield.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (jdBoundsRef.current) { try { map.removeLayer(jdBoundsRef.current); } catch (e) {} jdBoundsRef.current = null; }
    if (!showJdBounds || !jdFields || !jdFields.features.length) return;
    const esc = (v) => String(v == null ? "" : v).replace(/[<>&]/g, c => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
    const layer = L.geoJSON(jdFields, {
      style: (f) => {
        const p = f.properties; const sel = jdSel && p.jd_field_id === jdSel.jd_field_id;
        // Planting map: fill each field by its planted crop color.
        if (showJdPlanting) return { color: sel ? "#ffe600" : "#2d3f2d", weight: sel ? 3.5 : 1, opacity: 0.95, fill: true, fillColor: jdCropColor(p.plant_crop || p.harvest_crop), fillOpacity: 0.62 };
        // Default: JD-green outline + a faint fill (fill REQUIRED so interior
        // clicks hit the JD polygon instead of the CLU field beneath it).
        return { color: sel ? "#ffe600" : "#8be04e", weight: sel ? 3.5 : 2, opacity: 0.97, fill: true, fillColor: sel ? "#ffe600" : "#8be04e", fillOpacity: sel ? 0.24 : 0.09 };
      },
      onEachFeature: (f, lyr) => {
        const p = f.properties || {};
        lyr.on("click", (e) => {
          try { L.DomEvent.stopPropagation(e); } catch (_) {}
          setJdSel(p);
          const ready = (p.harvests || []).find(h => h.has_grid);   // auto-show a map that's already built
          if (ready) loadJdGrid(ready, p.jd_field_id); else setJdOp(null);
        });
        // No hover label on the SELECTED field. Its map is the thing being read,
        // and a sticky tooltip chasing the cursor across it covers the picture
        // while repeating what the picker panel already says. The yield readout
        // stays — that's the number you're hovering FOR. Unselected fields keep
        // their label, since it's the only thing naming them.
        if (jdSel && p.jd_field_id === jdSel.jd_field_id) return;
        // Label each fact with its OWN year. The old form read "planted CORN · 45.2
        // bu/ac", splicing a 2026 planting onto a 2025 soybean yield as if they were
        // one season — the two come from different operations and often different crops.
        lyr.bindTooltip(
          `${esc(p.field || "Field")}` +
          (p.plant_crop ? `<br>planted ${esc(p.plant_crop)}${p.plant_year ? ` ${esc(String(p.plant_year))}` : ""}` : "") +
          (p.harvest_yield != null
            ? `<br>${esc(String(p.harvest_crop || ""))}${p.harvest_year ? ` ${esc(String(p.harvest_year))}` : ""} · ${p.harvest_yield} ${jdYieldUnitLabel(p.harvest_crop, null)}`
            : ""),
          { sticky: true, direction: "top" });
      },
    }).addTo(map);
    try { layer.bringToFront(); } catch (e) {}
    jdBoundsRef.current = layer;
    return () => { if (jdBoundsRef.current) { try { map.removeLayer(jdBoundsRef.current); } catch (e) {} jdBoundsRef.current = null; } };
  }, [showJdBounds, showJdPlanting, jdFields, jdSel, L]);

  // Sub-field yield HEATMAP for the SELECTED field's SELECTED year (jdOp). Canvas
  // renderer + RdYlGn ramp (2–98 pct domain) so it reads like a JD yield map.
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    if (jdYieldRef.current) { try { map.removeLayer(jdYieldRef.current); } catch (e) {} jdYieldRef.current = null; }
    const g = jdOp && jdGrids[jdOp]; if (!g || !g.values || !g.bbox || jdOpsOn) return;
    const nx = g.nx, ny = g.ny, xmin = g.bbox[0], ymin = g.bbox[1], xmax = g.bbox[2], ymax = g.bbox[3];
    // Fill the sparse point cloud into full coverage (JD renders the swath area
    // solid), cached on the grid and keyed to the contour setting. The SCALE then
    // comes off that surface, not the raw cells — see jdFieldScaleOf.
    const vals = jdFilledValues(g, jdContour ? 3 : 0); if (!vals) return;
    const auto = jdFieldScaleOf(g, jdContour ? 3 : 0); if (!auto) return;
    const lo = (jdFieldScale && jdFieldScale.lo != null) ? jdFieldScale.lo : auto.lo;
    const hi = (jdFieldScale && jdFieldScale.hi != null) ? jdFieldScale.hi : auto.hi;
    if (!map.getPane("jdYieldPane")) { map.createPane("jdYieldPane"); const pn = map.getPane("jdYieldPane"); if (pn) pn.style.zIndex = 450; }
    let layer;
    if (jdContour && window.d3 && window.d3.contours) {
      // VECTOR contour bands (d3.contours) — resolution-independent, so the map
      // stays crisp at ANY zoom instead of pixelating like a raster PNG, and reads
      // as clean iso-yield zones (John Deere-style). Cells outside the swath (null)
      // get a sentinel below every threshold, so they fall outside all bands and
      // are masked out; d3's smoothing gives the curved band edges.
      const d3 = window.d3;
      const dx = (xmax - xmin) / nx, dy = (ymax - ymin) / ny;
      const sentinel = lo - (hi - lo) - 1;
      const dense = new Array(nx * ny);
      for (let i = 0; i < nx * ny; i++) { const v = vals[i]; dense[i] = (v == null ? sentinel : v); }
      const NB = 14; // number of colour bands between lo and hi
      const thr = d3.range(NB + 1).map((k) => lo + (hi - lo) * (k / NB));
      const bands = d3.contours().size([nx, ny]).thresholds(thr).smooth(true)(dense);
      const feats = bands.filter((c) => c.value >= lo && c.coordinates.length).map((c) => ({
        type: "Feature", properties: { v: c.value },
        geometry: { type: "MultiPolygon", coordinates: c.coordinates.map((poly) => poly.map((ring) => ring.map(([x, y]) => [xmin + x * dx, ymin + y * dy]))) },
      }));
      layer = L.geoJSON({ type: "FeatureCollection", features: feats }, {
        pane: "jdYieldPane", interactive: false,
        style: (f) => ({ stroke: false, fill: true, fillColor: jdYieldColor(f.properties.v, lo, hi), fillOpacity: 0.92 }),
      });
      layer.addTo(map);
    } else {
      // Raw raster fallback (contour toggled off, or d3 not loaded yet): nx×ny PNG.
      // Canvas Y is top-down so we flip rows.
      const cvs = document.createElement("canvas"); cvs.width = nx; cvs.height = ny;
      const ctx = cvs.getContext("2d"); const im = ctx.createImageData(nx, ny);
      for (let row = 0; row < ny; row++) for (let col = 0; col < nx; col++) {
        const v = vals[row * nx + col]; const idx = ((ny - 1 - row) * nx + col) * 4;
        if (v == null) { im.data[idx + 3] = 0; continue; }
        const m = jdYieldColor(v, lo, hi).match(/(\d+),\s*(\d+),\s*(\d+)/);
        im.data[idx] = +m[1]; im.data[idx + 1] = +m[2]; im.data[idx + 2] = +m[3]; im.data[idx + 3] = 235;
      }
      ctx.putImageData(im, 0, 0);
      layer = L.imageOverlay(cvs.toDataURL(), [[ymin, xmin], [ymax, xmax]], { opacity: 0.9, interactive: false, pane: "jdYieldPane", className: "jd-yield-raster" });
      layer.addTo(map);
      const el = layer.getElement(); if (el) el.style.imageRendering = "pixelated";
    }
    jdYieldRef.current = layer;
    // Interactive readout — hover over the field to see the bu/ac at that spot.
    const cell = g.cell;
    const onMove = (e) => {
      const lat = e.latlng.lat, lng = e.latlng.lng;
      if (lng < xmin || lng > xmax || lat < ymin || lat > ymax) { setJdHover(null); return; }
      const col = Math.floor((lng - xmin) / cell), row = Math.floor((lat - ymin) / cell);
      const v = (row >= 0 && row < ny && col >= 0 && col < nx) ? vals[row * nx + col] : null;
      setJdHover(v != null ? { v: +v.toFixed(1), unit: g.unit || "bu/ac", x: e.containerPoint.x, y: e.containerPoint.y } : null);
    };
    map.on("mousemove", onMove);
    return () => {
      try { map.off("mousemove", onMove); } catch (e) {}
      setJdHover(null);
      if (jdYieldRef.current) { try { map.removeLayer(jdYieldRef.current); } catch (e) {} jdYieldRef.current = null; }
    };
  }, [jdOp, jdGrids, L, jdOpsOn, jdContour, jdFieldScale, d3Ready]);

  // Load (or generate + cache) the yield grid for a chosen harvest year, then show
  // it. First-time generation runs the JD shapefile export (a few minutes); the
  // endpoint hands back a statusUrl while it builds, so we just keep polling.
  const loadJdGrid = (h, fieldId) => {
    // Drop any manual lo/hi when switching operations. The override persisted across
    // selections, so a scale set on a YIELD map (e.g. 29–68 bu/ac) was then applied to
    // a PLANTING map in seeds/ac (~32,000) — every cell clamped to the top of the ramp
    // and the field rendered as one solid block, which read as "planting isn't machine
    // data". Each grid re-derives its own 2nd/98th percentile scale.
    setJdFieldScale(null);
    setJdOp(h.op_id);
    if (jdGrids[h.op_id]) {
      const n = (jdGrids[h.op_id].values || []).filter(x => x != null).length;
      setJdMsg(n < MIN_MAP_CELLS ? `⚠ only ${n} point${n === 1 ? "" : "s"} from John Deere — nothing to map` : `✓ showing (${n} cells)`);
      return;
    }
    setJdLoading(h.op_id); setJdMsg("fetching…");
    const fid = fieldId || (jdSel && jdSel.jd_field_id);
    let tries = 0;
    const step = (statusurl) => {
      tries++;
      const u = `/api/market?fn=jd-yieldgrid&field=${fid}&op=${h.op_id}` +
        (h.yield != null ? `&bu=${h.yield}` : "") +
        (statusurl ? `&statusurl=${encodeURIComponent(statusurl)}` : "");
      authedFetch(u).then(r => r.json()).then(g => {
        if (g && g.pending && g.statusUrl) { setJdMsg(`building yield map… (${tries})`); step(g.statusUrl); return; }
        if (g && g.values) {
          // POST-CALIBRATION IS APPLIED HERE, ONCE, AT LOAD. Scaling the grid the moment it
          // arrives means the map, its colour scale, the legend bounds, the op average and
          // every hover readout are all the same corrected number — there is no second place
          // that could keep showing the raw one. An uncalibrated combine is wrong by a
          // farm-wide multiplier (Kennedy's 2026 wheat read 46.4 on a crop that made ~71),
          // and that error cancels out of the pattern, so the map's shape is trustworthy
          // even when its numbers are not. `_cal` rides along so the UI can say so.
          const cf = jdCalFactor(h.crop, h.date || h.op_date);
          const gg = cf && cf.factor > 0
            ? { ...g, values: g.values.map(v => v == null ? v : v * cf.factor),
                bu_mean: g.bu_mean != null ? Math.round(g.bu_mean * cf.factor * 10) / 10 : g.bu_mean,
                _cal: cf }
            : g;
          setJdGrids(prev => ({ ...prev, [h.op_id]: gg }));
          // A handful of John Deere exports come back degenerate — a single GPS
          // point, no acreage — and a one-cell grid draws nothing at map scale.
          // Reporting "✓ loaded 1 cells" for that reads as success and sends you
          // looking for a rendering bug that isn't there.
          const n = g.values.filter(x => x != null).length;
          setJdMsg(n < MIN_MAP_CELLS
            ? `⚠ John Deere returned only ${n} point${n === 1 ? "" : "s"} for this operation — nothing to map`
            : `✓ loaded ${n} cells`);
        }
        else { setJdMsg("error: " + JSON.stringify(g || {}).slice(0, 140)); }
        setJdLoading(null);
      }).catch((e) => { setJdMsg("fetch failed: " + String(e && e.message || e).slice(0, 120)); setJdLoading(null); });
    };
    step(null);
  };

  // Search a JD field by name → zoom to it, select it, auto-show a built map.
  const jumpToJdField = (name) => {
    if (!jdFields || !name || !mapRef.current || !L) return;
    const q = String(name).trim().toLowerCase();
    const feat = jdFields.features.find(f => String(f.properties.field || "").toLowerCase() === q)
      || jdFields.features.find(f => String(f.properties.field || "").toLowerCase().includes(q));
    if (!feat) return;
    try { mapRef.current.fitBounds(L.geoJSON(feat).getBounds(), { padding: [60, 60], maxZoom: 16 }); } catch (e) {}
    setJdSel(feat.properties);
    const ready = (feat.properties.harvests || []).find(h => h.has_grid);
    if (ready) loadJdGrid(ready, feat.properties.jd_field_id); else setJdOp(null);
  };

  // Resolve a pending "🚜 Yield map" click from an INSURED field's popup: match
  // that CLU to the John Deere field over it, select it, zoom, and paint the
  // newest map that's already built. Waits on jdFields, which is null while the
  // layer is fetching (the button usually turns the layer on itself).
  useEffect(() => {
    if (!jdWant || !L || !jdFields) return;
    // Don't match against the previous scope's boundaries: the button narrows the
    // fetch to this farm, and that request is still in flight on this render.
    if (jdFieldsSeqRef.current !== jdFetchRef.current) return;
    const map = mapRef.current; if (!map) return;
    const hit = jdFieldAtPoint(jdFields, jdWant.lat, jdWant.lng);
    setJdWant(null);
    if (!hit) {
      // Honest miss. The farm is connected, but no JD field covers THIS boundary —
      // not every insured acre is machine-recorded. Said in the same panel the
      // answer would have appeared in, rather than silently doing nothing.
      setJdSel({ field: jdWant.name, jd_field_id: "__nomatch__", harvests: [], plantings: [] });
      setJdOp(null);
      setJdMsg("no John Deere field covers this boundary");
      return;
    }
    const pr = hit.properties || {};
    try { map.fitBounds(L.geoJSON(hit).getBounds(), { padding: [50, 50], maxZoom: 16 }); } catch (e) {}
    setJdSel(pr);
    // Harvest before planting — "yield map" is what was asked for. Nothing built
    // yet still opens the picker, so a year can be chosen and generated.
    const ready = (pr.harvests || []).find(h => h.has_grid)
      || (pr.plantings || []).filter(x => x.op_id).find(x => x.has_grid);
    if (ready) loadJdGrid(ready, pr.jd_field_id);
    else {
      setJdOp(null);
      setJdMsg((pr.harvests || []).length ? "pick a year to build its map" : "no harvest data for this field");
    }
  }, [jdWant, jdFields, L]); // eslint-disable-line

  // ── Whole-operation comparison ────────────────────────────────
  // operations list (connected farms) once the mode is enabled
  useEffect(() => {
    // Never ask for the operation roster on the portal side. The UI no longer
    // offers this mode there, but the state it keys on is still reachable from a
    // stale render, and this list is the leak itself — other customers' names.
    if (isPortal || !jdOpsOn || jdOpsList) return;
    authedFetch(`/api/market?fn=jd-connected`).then(r => r.json()).then(d => {
      // Real farmer name (never the raw id, and never the parent-org name a group
      // member inherits). Dedup by farmer_id, sort by name. Include EVERYONE with
      // JD data — attributed farms (custom-harvested for them) can compare too:
      // hOpsgrids masks the operator's grid to their CLUs.
      const seen = new Set(); const list = [];
      for (const c of ((d && d.connections) || [])) {
        if (!c.farmer_id || seen.has(c.farmer_id)) continue;
        seen.add(c.farmer_id);
        list.push({ farmer_id: c.farmer_id, name: c.farmer_name || c.jd_org_name || c.farmer_id });
      }
      list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setJdOpsList(list); if (list.length && !jdOpsFarmer) setJdOpsFarmer(list[0].farmer_id);
    }).catch(() => setJdOpsList([]));
  }, [jdOpsOn]); // eslint-disable-line
  // If a single farm is selected and it has JD data, point the compare dropdown at
  // it — so "select a farm → Compare operation" defaults to THAT farm, not the
  // alphabetical first. Deps intentionally exclude jdOpsFarmer, so a manual dropdown
  // pick sticks; this only re-selects when compare turns on or the farm selection
  // changes.
  useEffect(() => {
    if (!jdOpsOn || !jdOpsList || !jdOpsList.length || selSet.size !== 1) return;
    const selId = [...selSet][0];
    if (jdOpsList.some(o => o.farmer_id === selId) && jdOpsFarmer !== selId) setJdOpsFarmer(selId);
  }, [jdOpsOn, jdOpsList, selSet]); // eslint-disable-line
  // available (crop, year) combos for the chosen operation
  useEffect(() => {
    if (isPortal || !jdOpsOn || !jdOpsFarmer) { setJdOpsManifest(null); return; }
    let alive = true;
    authedFetch(`/api/market?fn=jd-opsgrids&farmer=${jdOpsFarmer}&manifest=1&metric=yield`).then(r => r.json()).then(d => {
      if (!alive) return; const combos = (d && d.combos) || []; setJdOpsManifest(combos);
      if (combos.length) setJdOpsSel({ crop: combos[0].crop, year: String(combos[0].year || "") });
    }).catch(() => { if (alive) setJdOpsManifest([]); });
    return () => { alive = false; };
  }, [jdOpsOn, jdOpsFarmer]); // eslint-disable-line
  // fetch all grids for (operation, crop, year) + compute the shared scale
  useEffect(() => {
    if (isPortal || !jdOpsOn || !jdOpsFarmer || !jdOpsSel.crop || !jdOpsSel.year) { setJdOpsData(null); return; }
    let alive = true; setJdOpsBusy(true); setJdOpsData(null); setJdOpsScale(null);
    authedFetch(`/api/market?fn=jd-opsgrids&farmer=${jdOpsFarmer}&crop=${encodeURIComponent(jdOpsSel.crop)}&year=${jdOpsSel.year}&metric=yield`).then(r => r.json()).then(d => {
      if (!alive) return; const grids = (d && d.grids) || [];
      // Scale off the SMOOTHED surface with every field weighted equally. Pooling
      // raw cells stretched the ramp over start/stop noise (Sullivan 2024 corn:
      // p02–p98 = 36–247 on fields averaging 174–207) and every field collapsed
      // into one flat block. jdSharedScaleOf caches the surface it fills.
      const sc = jdSharedScaleOf(grids) || { lo: 0, hi: 1 };
      const all = []; for (const g of grids) for (const v of (g.values || [])) if (v != null) all.push(v);
      const mean = all.length ? all.reduce((s, x) => s + x, 0) / all.length : 0;
      // The unit is whatever the grids say it is — a chopper's maps are DM ton/ac —
      // and an operation mixing units says so rather than averaging them.
      const units = [...new Set(grids.map(g => g.unit || "bu/ac"))];
      setJdOpsData({ grids, lo: sc.lo, hi: sc.hi, mean: +mean.toFixed(1), fields: grids.length, cells: all.length, unit: units.length === 1 ? units[0] : "mixed units" });
      setJdOpsBusy(false);
    }).catch(() => { if (alive) setJdOpsBusy(false); });
    return () => { alive = false; };
  }, [jdOpsOn, jdOpsFarmer, jdOpsSel.crop, jdOpsSel.year]); // eslint-disable-line
  // render every field's grid on the ONE shared diverging scale
  useEffect(() => {
    const map = mapRef.current; if (!map || !L) return;
    jdOpsLayersRef.current.forEach(l => { try { map.removeLayer(l); } catch (e) {} }); jdOpsLayersRef.current = [];
    if (!jdOpsOn || !jdOpsData || !jdOpsData.grids.length) return;
    const lo = (jdOpsScale && jdOpsScale.lo != null) ? jdOpsScale.lo : jdOpsData.lo;
    const hi = (jdOpsScale && jdOpsScale.hi != null) ? jdOpsScale.hi : jdOpsData.hi;
    if (!map.getPane("jdYieldPane")) { map.createPane("jdYieldPane"); const pn = map.getPane("jdYieldPane"); if (pn) pn.style.zIndex = 450; }
    for (const g of jdOpsData.grids) {
      if (!g.values || !g.bbox) continue;
      const nx = g.nx, ny = g.ny, xmin = g.bbox[0], ymin = g.bbox[1], xmax = g.bbox[2], ymax = g.bbox[3];
      const vals = jdFilledValues(g, jdContour ? 3 : 0); if (!vals) continue;
      if (jdContour && window.d3 && window.d3.contours) {
        // VECTOR contour bands — crisp at any zoom, JD-style (see single-field map).
        const d3 = window.d3;
        const dx = (xmax - xmin) / nx, dy = (ymax - ymin) / ny;
        const sentinel = lo - (hi - lo) - 1;
        const dense = new Array(nx * ny);
        for (let i = 0; i < nx * ny; i++) { const v = vals[i]; dense[i] = (v == null ? sentinel : v); }
        const NB = 14;
        const thr = d3.range(NB + 1).map((k) => lo + (hi - lo) * (k / NB));
        const bands = d3.contours().size([nx, ny]).thresholds(thr).smooth(true)(dense);
        const feats = bands.filter((c) => c.value >= lo && c.coordinates.length).map((c) => ({
          type: "Feature", properties: { v: c.value },
          geometry: { type: "MultiPolygon", coordinates: c.coordinates.map((poly) => poly.map((ring) => ring.map(([x, y]) => [xmin + x * dx, ymin + y * dy]))) },
        }));
        const layer = L.geoJSON({ type: "FeatureCollection", features: feats }, {
          pane: "jdYieldPane", interactive: false,
          style: (f) => ({ stroke: false, fill: true, fillColor: jdOpsColor(f.properties.v, lo, hi), fillOpacity: 0.92 }),
        });
        layer.addTo(map); jdOpsLayersRef.current.push(layer);
      } else {
        const cvs = document.createElement("canvas"); cvs.width = nx; cvs.height = ny;
        const ctx = cvs.getContext("2d"); const im = ctx.createImageData(nx, ny);
        for (let row = 0; row < ny; row++) for (let col = 0; col < nx; col++) {
          const v = vals[row * nx + col]; const idx = ((ny - 1 - row) * nx + col) * 4;
          if (v == null) { im.data[idx + 3] = 0; continue; }
          const m = jdOpsColor(v, lo, hi).match(/(\d+),\s*(\d+),\s*(\d+)/);
          im.data[idx] = +m[1]; im.data[idx + 1] = +m[2]; im.data[idx + 2] = +m[3]; im.data[idx + 3] = 235;
        }
        ctx.putImageData(im, 0, 0);
        const layer = L.imageOverlay(cvs.toDataURL(), [[ymin, xmin], [ymax, xmax]], { opacity: 0.92, interactive: false, pane: "jdYieldPane" });
        layer.addTo(map); const el = layer.getElement(); if (el) el.style.imageRendering = "pixelated";
        jdOpsLayersRef.current.push(layer);
      }
    }
    // Hover readout — find whichever field's grid is under the cursor, read its cell.
    const onMove = (e) => {
      const lat = e.latlng.lat, lng = e.latlng.lng;
      for (const g of jdOpsData.grids) {
        const b = g.bbox; if (!b || lng < b[0] || lng > b[2] || lat < b[1] || lat > b[3]) continue;
        const col = Math.floor((lng - b[0]) / g.cell), row = Math.floor((lat - b[1]) / g.cell);
        if (row >= 0 && row < g.ny && col >= 0 && col < g.nx) {
          const v = g.values[row * g.nx + col];
          if (v != null) { setJdHover({ v, unit: "bu/ac", field: g.field, x: e.containerPoint.x, y: e.containerPoint.y }); return; }
        }
      }
      setJdHover(null);
    };
    map.on("mousemove", onMove);
    return () => {
      try { map.off("mousemove", onMove); } catch (e) {}
      setJdHover(null);
      jdOpsLayersRef.current.forEach(l => { try { map.removeLayer(l); } catch (e) {} }); jdOpsLayersRef.current = [];
    };
  }, [jdOpsOn, jdOpsData, jdOpsScale, L, jdContour, d3Ready]);

  // which farmers actually have a boundary/pin in view (for the tree's "—" marker)
  const mapped = useMemo(() => {
    const s = new Set();
    if (geoms) for (const r of rows) { const k = (r.clu_id||"").toLowerCase(); if ((k && geoms[k]) || r.latitude != null) s.add(r.farmer_id); }
    return s;
  }, [rows, geoms]);

  // Preserve the incoming `groups` order (FieldDataView controls grower sort). The
  // rail carried its OWN "Filter farms…" box until 2026-08-13; it matched on farm
  // name only, which is a strict subset of what the screen's search box already
  // does (farmer, policy, FSN, tract, field) over the rows these groups are built
  // from. Two boxes, one of which could not answer anything the other couldn't.
  const farms = groups || [];
  // Clicking a farm in the rail selects it — same single selection the toolbar
  // picker writes, never a second one.
  const pickFarm = (id) => { if (onSelectFarm) onSelectFarm(id); };

  // Export the field polygons currently on the map (selected farms, or all in
  // view if none selected) as a zipped shapefile — for handing growers/FSA their
  // boundaries. @mapbox/shp-write loaded on demand (UMD global `shpwrite`).
  const exportShapefile = async () => {
    if (!fc || !fc.features.length) return;
    setExporting(true);
    try {
      if (!window.shpwrite) {
        await new Promise((res, rej) => { const s = document.createElement("script"); s.src = "https://unpkg.com/@mapbox/shp-write@0.4.3/shpwrite.js"; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
      }
      const exp = { type:"FeatureCollection", features: fc.features.map(f => {
        const p = f.properties;
        // FIELD must be UNIQUE per parcel — the bare FSA field number repeats
        // across tracts (Kirby reuses "1"/"3"), so GIS tools like Climate
        // FieldView dissolve same-number parcels into one field. Use the full
        // FSN-Tract-Field path so each CLU imports as its own field.
        const fieldKey = [p.fsn, p.tract, p.field].filter(Boolean).join("-") || (p.fsn || p.field || "");
        return {
          type:"Feature", geometry: f.geometry,
          properties: {
            CLIENT:  (p.farmer || "").slice(0, 50),
            FARM:    p.fsn || "",
            TRACT:   p.tract || "",
            FIELD:   fieldKey,
            FSA_FLD: p.field || "",
            CROP:    p.crop || "",
            ACRES:   p.acres != null ? Number(p.acres) : 0,
            COUNTY:  p.county || "",
            PLANTED: p.planted || "",
            STATUS:  p.pp ? "PREVENT PLANT" : "PLANTED",
          },
        };
      }) };
      let out = window.shpwrite.zip(exp, { outputType:"blob", compression:"DEFLATE", types:{ polygon:"fields", point:"field_points" } });
      if (out && typeof out.then === "function") out = await out;
      let blob;
      if (out instanceof Blob) blob = out;
      else if (typeof out === "string") { const bin = atob(out); const arr = new Uint8Array(bin.length); for (let i=0;i<bin.length;i++) arr[i]=bin.charCodeAt(i); blob = new Blob([arr], { type:"application/zip" }); }
      else blob = new Blob([out], { type:"application/zip" });
      const who = selSet.size ? (fc.features[0]?.properties.farmer || "farm") : "all-fields";
      const safe = String(who).replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "").slice(0, 40) || "fields";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${safe}-shapefile.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) { alert("Shapefile export failed: " + e.message); }
    finally { setExporting(false); }
  };

  // ── PRINT FIELD MAPS ──────────────────────────────────────────────────────
  // Groups the fields on the map by proximity (groupFieldsForPrint) and prints
  // ONE PAGE PER GROUP — satellite imagery, boundaries in their crop colours,
  // numbered labels, and a field table keyed to those numbers.
  //
  // Prints whatever the MAP is currently showing, so the crop / corn-type /
  // county filters compose with it — "print the silage ground in Genesee" is
  // just two filters and this button, with no separate scoping UI to keep in step.
  // ONE grouping serves both the page-count preview and the print itself. Computing
  // it twice is how a preview comes to promise 6 pages and print 7.
  const printPages = useMemo(() => {
    // NOT COMPUTED UNLESS THE PANEL IS OPEN. The book-wide view holds 14,649 fields
    // and this ran on every render of the Map tab — work nobody asked for, on the
    // one screen where the map itself is already the expensive thing.
    if (!printOpen) return [];
    // POLYGONS ONLY. `fc.features` also carries POINT markers for fields we hold no
    // boundary for, and a point has nothing to draw on a printed map. Worse, feeding
    // one to _geomBbox crashes the app: its walker recurses until it hits a number,
    // and a Point's coordinates ARE two bare numbers, so it called .forEach on a
    // number and took the whole Map tab down with it.
    const feats = ((fc && fc.features) || []).filter(f =>
      f && f.geometry && (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon"));
    if (!feats.length) return [];
    const items = feats.map(f => {
      const b = _geomBbox(f.geometry);            // [minLon, minLat, maxLon, maxLat]
      return { key: f.properties.clu_id || `${f.properties.fsn}-${f.properties.field_id}`,
               lat: (b[1] + b[3]) / 2, lon: (b[0] + b[2]) / 2, f,
               bbox: { minLat: b[1], maxLat: b[3], minLon: b[0], maxLon: b[2] } };
    });
    // Union each group's FIELD bboxes, not just its centroids — a centroid-only
    // bbox crops the outer fields' edges off the page.
    return groupFieldsForPrint(items, printSpanMi).map(g => ({
      bbox: g.items.map(i => i.bbox).reduce(_bboxUnion),
      fields: g.items.map(i => i.f),
    }));
  }, [fc, printSpanMi, printOpen]);
  const printBiggest = useMemo(
    () => printPages.reduce((m, p) => Math.max(m, p.fields.length), 0), [printPages]);

  const printFieldMaps = () => {
    const feats = (fc && fc.features) || [];
    if (!feats.length) { alert("No fields on the map to print."); return; }
    const pages = printPages;
    if (!pages.length) { alert("No field boundaries available to print."); return; }

    const who = selSet.size ? (feats[0]?.properties.farmer || "Field maps") : "All fields";
    const w = window.open("", "_blank");
    if (!w) { alert("Pop-up blocked — allow pop-ups to print the maps."); return; }
    writePrintWindow(w, buildFieldMapsPrintHtml(pages, {
      title: who,
      subtitle: [!selSet.size ? null : (feats[0]?.properties.county
        ? feats[0].properties.county + " Co." + (feats[0].properties.state ? ", " + feats[0].properties.state : "")
        : null)].filter(Boolean).join(" · "),
      labels: labels || {},
    }));
  };

  // ── FSA PLANTING REPORT ───────────────────────────────────────────────────
  // Per-farm, because that is the unit an acreage report is filed in. Fetched only
  // when the tab is chosen and a single farm is selected — a book-wide FSA report is
  // not a thing anyone submits.
  const fsaFarmerId = selSet.size === 1 ? [...selSet][0] : null;
  // CROP YEAR IS THE CALLER'S CHOICE, not derived. Testing 2026 wheat means asking
  // for planting done in FALL 2025, which no "latest year in policy_fields" rule
  // would ever land on. null = let the server pick its default on the first load;
  // the year list it returns then drives the picker.
  const [fsaYearSel, setFsaYearSel] = useState(null);
  const fsaYear = fsaYearSel != null ? fsaYearSel : (fsa && fsa.year) || null;
  // Reset the picker when the farm changes — one farm's years are not another's.
  useEffect(() => { setFsaYearSel(null); setFsaCrops(new Set()); }, [fsaFarmerId]);

  useEffect(() => {
    if (isPortal || !printOpen || printKind !== "fsa" || !fsaFarmerId) { setFsa(null); return; }
    let alive = true; setFsaBusy(true); setFsaAck(false);
    authedFetch(`/api/market?fn=jd-fsaplanting&farmer=${encodeURIComponent(fsaFarmerId)}` +
      (fsaYearSel != null ? `&year=${fsaYearSel}` : ""))
      .then(r => r.json())
      .then(d => { if (alive) setFsa(d && !d.error ? d : { error: (d && d.error) || "no response" }); })
      .catch(e => { if (alive) setFsa({ error: String((e && e.message) || e) }); })
      .finally(() => { if (alive) setFsaBusy(false); });
    return () => { alive = false; };
  }, [isPortal, printOpen, printKind, fsaFarmerId, fsaYearSel]);

  // CROP IS PART OF THE DOCUMENT, not a view toggle — FSA acreage is filed per crop
  // and the deadlines differ (spring vs fall-seeded). Corn splits grain/silage
  // because the 578 reports those as separate crops, which is the whole reason the
  // type work earlier today mattered.
  // The MACHINE's crop already separates silage — John Deere records CORN and
  // CORN SILAGE as different crops — so no type_code lookup is needed here. The
  // report is driven by planting passes, and policy type codes may not exist yet
  // for the year it covers.
  const fsaCropKey = (r) => String(r.crop || "—").toUpperCase();
  const fsaCropOpts = useMemo(() => {
    const m = new Map();
    for (const r of ((fsa && fsa.rows) || [])) {
      const k = fsaCropKey(r);
      const c = m.get(k) || { k, fields: 0, acres: 0 };
      c.fields++; c.acres += r.planted_acres || 0;
      m.set(k, c);
    }
    return [...m.values()].sort((a, b) => b.acres - a.acres);
  }, [fsa]);

  // THE FILTERED SET IS THE DOCUMENT. Everything below — totals, flags, the printed
  // pages — is derived from THIS, never from fsa.summary, which the server computed
  // over every crop. Showing a whole-farm total above a corn-only table would be a
  // wrong number on a form headed to a federal agency.
  const fsaRows = useMemo(() => {
    const all = (fsa && fsa.rows) || [];
    return fsaCrops.size ? all.filter(r => fsaCrops.has(fsaCropKey(r))) : all;
  }, [fsa, fsaCrops]);

  // Exceptions follow the crop filter too — a wheat-only report must not carry a
  // corn field in its exceptions page.
  const fsaExceptions = useMemo(() => {
    const all = (fsa && fsa.exceptions) || [];
    return fsaCrops.size ? all.filter(e => fsaCrops.has(String(e.crop || "").toUpperCase())) : all;
  }, [fsa, fsaCrops]);

  const fsaSummary = useMemo(() => {
    const sum = (arr, f) => arr.reduce((s, r) => s + (f(r) || 0), 0);
    const un = fsaExceptions.filter(e => e.kind === "unmapped" || e.kind === "partly_unmapped");
    const nc = fsaExceptions.filter(e => e.kind === "no_coverage_yet");
    return {
      fields: fsaRows.length,
      planted_acres: +sum(fsaRows, r => r.planted_acres).toFixed(1),
      with_policy_acres: fsaRows.filter(r => r.policy_acres != null).length,
      implausible_dates: fsaRows.filter(r => r.plant_date && r.plant_date_sane === false).length,
      unmapped_fields: un.length,
      unmapped_acres: +sum(un, e => e.unmapped_acres).toFixed(1),
      no_coverage_ops: nc.length,
      no_coverage_acres: +sum(nc, e => e.machine_acres).toFixed(1),
    };
  }, [fsaRows, fsaExceptions]);

  // Anything an agent must look at before this leaves the building. Not warnings —
  // a signed-off list. The PDF goes to a federal agency on the grower's behalf.
  const fsaFlags = useMemo(() => {
    if (!fsaRows.length && !fsaExceptions.length) return [];
    const out = [], s = fsaSummary;
    if (s.unmapped_acres > 0.5) out.push({ k: "unmapped", n: s.unmapped_fields,
      msg: `${s.unmapped_acres.toLocaleString()} planted acres fall outside every mapped FSA field — likely new ground the grower must add at FSA` });
    if (s.no_coverage_acres > 0.5) out.push({ k: "nogrid", n: s.no_coverage_ops,
      msg: `${s.no_coverage_acres.toLocaleString()} planted acres have no coverage map built yet — our processing, not the grower's boundaries` });
    if (s.implausible_dates) out.push({ k: "date", n: s.implausible_dates,
      msg: `${s.implausible_dates} plant date${s.implausible_dates === 1 ? "" : "s"} outside the crop's planting window` });
    return out;
  }, [fsaRows, fsaExceptions, fsaSummary]);

  // ── THE ACTUAL MACHINE PLANTING RASTER, prepared for print ─────────────────
  // Rasterised HERE, in the parent, and shipped as data URIs. The print window has
  // no d3 and no grid data; giving it finished images keeps it to Leaflet plus
  // <img>, which is what makes the tile-load gate the only timing concern.
  //
  // THE COLOUR SCALE IS PER CROP. Seed rates across a farm span 38 to 108,492 in one
  // pool — corn ~32k, soybeans ~150k, wheat drilled by weight — so one shared scale
  // renders every corn field a single flat colour. Each crop gets its own 5th/95th
  // percentile range, which is also what makes the legend honest.
  const fsaGridsFor = async (rowsIn, year) => {
    if (!fsaFarmerId) return [];
    const man = await authedFetch(`/api/market?fn=jd-opsgrids&farmer=${encodeURIComponent(fsaFarmerId)}&metric=seed_rate&manifest=1`)
      .then(r => r.json()).catch(() => null);
    const combos = (man && man.combos) || [];
    const wanted = new Set(rowsIn.map(r => String(r.crop || "").toUpperCase()));
    // A fall-seeded crop is drilled the PRIOR calendar year, and this endpoint filters
    // on the operation date — so CY2026 wheat lives under year 2025. Same roll
    // rmaCropYear() applies server-side; without it wheat silently has no raster.
    const want = combos.filter(c => {
      const cu = String(c.crop || "").toUpperCase();
      if (!wanted.has(cu) && !wanted.has(cu.split(" ")[0])) return false;
      const fall = /WHEAT|TRITICALE/.test(cu);
      return c.year === year || (fall && c.year === year - 1);
    });
    const out = [];
    for (const c of want) {
      const d = await authedFetch(`/api/market?fn=jd-opsgrids&farmer=${encodeURIComponent(fsaFarmerId)}` +
        `&metric=seed_rate&crop=${encodeURIComponent(c.crop)}&year=${c.year}`)
        .then(r => r.json()).catch(() => null);
      for (const g of ((d && d.grids) || [])) if (g.values && g.bbox) out.push({ ...g, _crop: String(c.crop || "").toUpperCase() });
    }
    // Per-crop 5th/95th percentile, so one bad cell can't flatten the ramp.
    const byCrop = {};
    for (const g of out) (byCrop[g._crop] = byCrop[g._crop] || []).push(g);
    const scale = {};
    for (const [cropK, gs] of Object.entries(byCrop)) {
      const vs = [];
      for (const g of gs) for (const v of g.values) if (v != null && v > 0) vs.push(v);
      vs.sort((a, b) => a - b);
      scale[cropK] = vs.length
        ? { lo: vs[Math.floor(vs.length * 0.05)], hi: vs[Math.floor(vs.length * 0.95)] }
        : { lo: 0, hi: 1 };
    }
    // Downsample anything past 520px — a page map is ~4in wide and the extra
    // resolution is pure payload. Sullivan's largest grid is 692x389.
    const MAXPX = 520;
    return out.map(g => {
      const vals = jdFilledValues(g, 0); if (!vals) return null;
      const sc = scale[g._crop] || { lo: 0, hi: 1 };
      const step = Math.max(1, Math.ceil(Math.max(g.nx, g.ny) / MAXPX));
      const w = Math.max(1, Math.floor(g.nx / step)), h = Math.max(1, Math.floor(g.ny / step));
      const cvs = document.createElement("canvas"); cvs.width = w; cvs.height = h;
      const ctx = cvs.getContext("2d"); const im = ctx.createImageData(w, h);
      for (let row = 0; row < h; row++) for (let col = 0; col < w; col++) {
        const v = vals[Math.min(g.ny - 1, row * step) * g.nx + Math.min(g.nx - 1, col * step)];
        const idx = ((h - 1 - row) * w + col) * 4;
        if (v == null || !(v > 0)) { im.data[idx + 3] = 0; continue; }
        const m = jdOpsColor(v, sc.lo, sc.hi).match(/(\d+),\s*(\d+),\s*(\d+)/);
        im.data[idx] = +m[1]; im.data[idx + 1] = +m[2]; im.data[idx + 2] = +m[3]; im.data[idx + 3] = 225;
      }
      ctx.putImageData(im, 0, 0);
      return { url: cvs.toDataURL("image/png"), crop: g._crop,
               bounds: [[g.bbox[1], g.bbox[0]], [g.bbox[3], g.bbox[2]]],
               lo: Math.round(sc.lo), hi: Math.round(sc.hi) };
    }).filter(Boolean);
  };

  const [fsaPrinting, setFsaPrinting] = useState(false);
  const printFsaReport = async () => {
    if (!fsaRows.length || fsaPrinting) return;
    const name = (groups.find(g => g.id === fsaFarmerId) || {}).name || "Farm";
    const geomFor = {};
    for (const r of fsaRows) {
      const k = String(r.clu_id || "").toLowerCase();
      if (geoms && geoms[k]) geomFor[k] = geoms[k];
    }
    // Open the window BEFORE the await. A window.open() after an async gap is no
    // longer tied to the click and browsers block it as a pop-up.
    const w = window.open("", "_blank");
    if (!w) { alert("Pop-up blocked — allow pop-ups to print the report."); return; }
    writePrintWindow(w, '<p style="font:15px -apple-system,sans-serif;color:#1f3320;padding:40px">Building planting report…</p>');
    setFsaPrinting(true);
    let rasters = [];
    try { rasters = await fsaGridsFor(fsaRows, fsa.year); }
    catch (e) { /* boundaries + table still print; the raster is the extra */ }
    setFsaPrinting(false);
    // The FILTERED rows and the summary recomputed over them — never fsa.summary,
    // which covers every crop and would head a corn-only report with whole-farm acres.
    writePrintWindow(w, buildFsaPlantingPrintHtml(
      { rows: fsaRows, exceptions: fsaExceptions, summary: fsaSummary, year: fsa.year },
      { farmer: name, year: fsa.year, geoms: geomFor, spanMi: printSpanMi, labels: labels || {},
        crops: fsaCrops.size ? [...fsaCrops] : null, rasters }));
  };

  // Stats for the selected farms (respecting the active crop/county filters via `rows`).
  // Bushel/revenue guarantee only counts crops actually in view per farmer.
  const selStats = useMemo(() => {
    const target = rows.filter(r => !selSet.size || selSet.has(r.farmer_id));
    const counties = new Set(), fids = new Set(), fcCrops = {}; let acres = 0;
    for (const r of target) {
      const a = parseFloat(r.acres); acres += Number.isFinite(a) ? a : (cluAcres[(r.clu_id||"").toLowerCase()] || 0);
      if (r.county) counties.add(r.county);
      fids.add(r.farmer_id);
      if (r.crop) (fcCrops[r.farmer_id] = fcCrops[r.farmer_id] || new Set()).add(r.crop);
    }
    const byCrop = {}; let revGuar = 0;
    if (guar) for (const fid of fids) {
      const g = guar[fid]; if (!g) continue;
      const crops = fcCrops[fid] || new Set();
      for (const [c, v] of Object.entries(g.byCrop)) {
        if (crops.size && !crops.has(c)) continue;     // honor the crop filter
        (byCrop[c] = byCrop[c] || { bu:0 }).bu += v.bu; revGuar += v.liab;
      }
    }
    return { fields: target.length, acres, counties: counties.size, farmers: fids.size, byCrop, revGuar };
  }, [rows, selSet, guar, cluAcres]);

  // Map height tracks the VIEWPORT rather than a fixed 620px. The map is the whole
  // point of this screen, and on a 1440-tall display a fixed 620 left it occupying
  // under half the window with dead space beneath. Clamped so it never gets so tall
  // that the page scrolls just to reach the legend, nor so short it's unusable.
  // Map real estate. Three fixed columns plus a full-width Overlays card stacked
  // above the map left the map itself a narrow strip — the opposite of the goal.
  // Both side panels collapse to a thin handle, and the overlay controls collapse
  // to one button, so the map can have the screen when you want it.
  // The insured CLU field clicked on the map — the inspector's field scope. Kept
  // separate from jdSel (John Deere boundary properties), which is a different shape.
  const [selField, setSelField]     = useState(null);

  // Open by default on a desktop (2026-10-31 redesign: list | map | field card). The toolbar
  // farm picker is still the selector; the rail is the field browser beside the map.
  const [railOpen, setRailOpen]     = useState(!isMobile);
  const [layersOpen, setLayersOpen] = useState(false);

  // ── PHONE MODE ──────────────────────────────────────────────────────────────
  // Set by FieldDataView only on a phone, on the Map tab. The map fills the screen
  // and ONE bottom sheet carries what the rail, the stats strip and the field card
  // carry on a desktop: min (just its title) · peek (the farm at a glance) · list
  // (every field) · field (one field's page). The Layers panel becomes a sheet too.
  const [sheet, setSheet] = useState("peek");
  const [listSort, setListSort] = useState("acres");   // acres | name
  const [renaming, setRenaming] = useState(false);
  const [topNode, setTopNode] = useState(null);
  const [topH, setTopH] = useState(0);
  const [sheetNode, setSheetNode] = useState(null);
  const [sheetH, setSheetH] = useState(0);
  useEffect(() => {
    if (!topNode) { setTopH(0); return; }
    const measure = () => setTopH(topNode.offsetHeight || 0);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(topNode);
    return () => ro.disconnect();
  }, [topNode]);
  useEffect(() => {
    if (!sheetNode) { setSheetH(0); return; }
    const measure = () => setSheetH(sheetNode.offsetHeight || 0);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(sheetNode);
    return () => ro.disconnect();
  }, [sheetNode]);
  selFieldRef.current = selField;
  const sheetEff = sheet === "field" && !selField ? "peek" : sheet;
  // Everything that floats on the map's bottom edge sits on top of the sheet instead.
  const bIn = phone ? sheetH : 0;
  // A tall sheet or the Layers sheet leaves no room for the legend or the radar bar.
  const phoneBusy = !!phone && (layersOpen || sheetEff === "list" || sheetEff === "field");
  const phoneOn = !!phone;
  // ── THE PHONE'S BACK BUTTON CLOSES THESE SHEETS (FieldDataView owns the history
  // step). Report whether one is up, and lend the closer: Layers first, then a field's
  // sheet or the list, each back to the summary.
  const phoneChop = !!(phone && phone.chopPanel);
  const phoneSubOpen = phoneBusy && !phoneChop;
  useEffect(() => {
    const p = phoneRef.current;
    if (p && p.onSubChange) p.onSubChange(phoneSubOpen);
  }, [phoneSubOpen]);
  useEffect(() => () => { const p = phoneRef.current; if (p && p.onSubChange) p.onSubChange(false); }, []);
  if (phone && phone.subCloseRef) {
    phone.subCloseRef.current = () => {
      if (layersOpen) { setLayersOpen(false); setSheet(showRadar || showPrecip ? "min" : "peek"); return; }
      if (sheetEff === "field") { setSelField(null); setSheet("peek"); return; }
      if (sheetEff === "list") setSheet("peek");
    };
  }
  useEffect(() => {
    fitPadRef.current = phoneOn ? { top: topH + 16, bottom: sheetH + 16 } : null;
  }, [phoneOn, topH, sheetH]);

  // The tapped field gets a white outline, on a desktop and a phone alike, so the
  // card or page is visibly ABOUT that field. A separate non-interactive layer, so
  // the hatch/stripe fills painted onto the field paths are never restyled.
  const selHlRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (selHlRef.current && map) { try { map.removeLayer(selHlRef.current); } catch (e) {} }
    selHlRef.current = null;
    if (!map || !L || !selField || !selField.geom || selField.geom.type === "Point") return;
    try {
      selHlRef.current = L.geoJSON({ type: "Feature", geometry: selField.geom },
        { style: { color: "#ffffff", weight: 3.5, opacity: 1, fill: false }, interactive: false }).addTo(map);
    } catch (e) { selHlRef.current = null; }
  }, [selField, L]);

  // On a phone a tapped field opens its page in the sheet and the map frames the field
  // in the strip left above it. Framed once per field, not on every re-render of it.
  const lastFitCluRef = useRef(null);
  const topHRef = useRef(0);
  topHRef.current = topH;
  useEffect(() => {
    if (!phoneOn) return;
    if (!selField) { lastFitCluRef.current = null; return; }
    setSheet("field"); setLayersOpen(false); setRenaming(false);
    const key = (selField.p && selField.p.clu_id) || null;
    if (key && key === lastFitCluRef.current) return;
    lastFitCluRef.current = key;
    const map = mapRef.current; if (!map || !L) return;
    const t = setTimeout(() => {
      try {
        const g = selField.geom;
        const h = (phone && phone.height) || 600;
        if (g && g.type !== "Point") {
          map.fitBounds(L.geoJSON({ type: "Feature", geometry: g }).getBounds(),
            { paddingTopLeft: [28, (topHRef.current || 100) + 20], paddingBottomRight: [28, Math.round(h * 0.6)], maxZoom: 16 });
        } else if (selField.center) map.panTo(selField.center);
      } catch (e) {}
    }, 40);
    return () => clearTimeout(t);
  }, [selField, L]);   // eslint-disable-line

  // "Where am I?" — the blue dot, and which of these fields it is standing in.
  const [locating, setLocating] = useState(false);
  const [meToast, setMeToast] = useState(null);   // { kind: in|out|err, feat?, text? }
  const meLayerRef = useRef(null);
  const locateMe = () => {
    if (!L) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setMeToast({ kind: "err", text: "This browser can't share your location." }); return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition((pos) => {
      setLocating(false);
      const map = mapRef.current; if (!map || !L) return;
      const lat = pos.coords.latitude, lng = pos.coords.longitude, acc = Math.min(pos.coords.accuracy || 0, 2000);
      try { if (meLayerRef.current) map.removeLayer(meLayerRef.current); } catch (e) {}
      try {
        meLayerRef.current = L.layerGroup([
          L.circle([lat, lng], { radius: acc, color: "#3b82f6", weight: 1, opacity: 0.5, fillColor: "#3b82f6", fillOpacity: 0.15, interactive: false }),
          L.circleMarker([lat, lng], { radius: 7, color: "#ffffff", weight: 3, fillColor: "#3b82f6", fillOpacity: 1, interactive: false }),
        ]).addTo(map);
      } catch (e) { meLayerRef.current = null; }
      let hit = null;
      for (const f of (fc && fc.features) || []) {
        if (f.geometry && f.geometry.type !== "Point" && fmGeomContains(f.geometry, lng, lat)) { hit = f; break; }
      }
      try { map.setView([lat, lng], Math.max(map.getZoom() || 0, 15)); } catch (e) {}
      setMeToast(hit ? { kind: "in", feat: hit } : { kind: "out", text: "You're not inside one of the fields on this map." });
    }, (err) => {
      setLocating(false);
      setMeToast({ kind: "err", text: err && err.code === 1
        ? "Location is off for this site. Turn it on in your phone's settings to use this."
        : "Couldn't get your location. Try again in a moment." });
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
  };

  // The farm at a glance, for the sheet's summary. Reported acres only, so the
  // figure is the insured one and never a boundary measurement.
  const phoneSum = useMemo(() => {
    if (!phoneOn) return null;
    const keys = new Set(), counties = new Set(), byCrop = new Map();
    let acres = 0;
    const prfInView = {};   // policy_id -> PRF fields of that policy in view, reported with no acres
    for (const r of rows) {
      if (selSet.size && !selSet.has(r.farmer_id)) continue;
      keys.add(cluLower(r.clu_id) || `${r.fsn}-${r.tract}-${r.field_id}`);
      const cty = r.insured_county || r._physCounty || r.county;
      if (cty) counties.add(String(cty).toUpperCase());
      const ac = r.acres != null ? (Number(r.acres) || 0) : 0;
      acres += ac;
      const crop = String(r.crop || "").toUpperCase(); if (!crop) continue;
      if (crop === "PRF" && r.acres == null && r.policy_id) prfInView[r.policy_id] = (prfInView[r.policy_id] || 0) + 1;
      const tc = crop === "CORN" ? String(r.type_code == null ? "" : r.type_code).trim() : "";
      const k = crop + "|" + tc;
      const e = byCrop.get(k) || { k, crop, tc, acres: 0, color: fieldCropColor(crop, tc), name: fmCropName(crop, tc) };
      e.acres += ac; byCrop.set(k, e);
    }
    // ⚠ PRF IS INSURED BY GRID, SO ITS FIELDS CARRY NO ACRES. The acres are the PRF
    // policy's insured acres (Kevin, 2026-11-01: Sparta Farms read "PRF 0" beside a policy
    // insuring 510.68 ac). A policy's acres are counted only when EVERY one of its PRF
    // fields is in view: a search or filter showing part of a policy cannot honestly
    // claim all of its acres, and the CLU boundaries are no stand-in (they match the
    // policy on 12 of 42 PRF policies in the 2026 book).
    if (prfAcres && prfAcres.byPolicy && prfAcres.fieldsByPolicy) {
      for (const [pid, n] of Object.entries(prfInView)) {
        const pa = prfAcres.byPolicy[pid];
        if (!(pa > 0) || n !== prfAcres.fieldsByPolicy[pid]) continue;
        acres += pa;
        const e = byCrop.get("PRF|") || { k: "PRF|", crop: "PRF", tc: "", acres: 0, color: fieldCropColor("PRF", ""), name: fmCropName("PRF", "") };
        e.acres += pa; byCrop.set("PRF|", e);
      }
    }
    return { fields: keys.size, acres, counties: counties.size, crops: [...byCrop.values()].sort((a, b) => b.acres - a.acres) };
  }, [phoneOn, rows, selSet, prfAcres]);

  const featByClu = useMemo(() => {
    const m = new Map();
    for (const f of (fc && fc.features) || []) { const k = cluLower(f.properties && f.properties.clu_id); if (k && !m.has(k)) m.set(k, f); }
    return m;
  }, [fc]);
  // The sheet's list: the farms when there is a farm to pick, else that farm's fields.
  const phoneList = useMemo(() => {
    if (!phoneOn || sheetEff !== "list") return null;
    const fl = groups || [];
    if (fl.length > 1 && !selectedFarmerId) {
      // Reported acres only, the same figure as the summary above, never a boundary measure.
      const rep = new Map();
      for (const r of rows || []) if (r.acres != null) rep.set(r.farmer_id, (rep.get(r.farmer_id) || 0) + (Number(r.acres) || 0));
      const items = fl.map(f => (rep.has(f.id) ? { ...f, acres: rep.get(f.id) } : f));
      return { mode: "farms", items, acres: items.reduce((a, f) => a + (Number(f.acres) || 0), 0) };
    }
    const by = new Map(), seen = new Set();
    for (const f of fl) for (const r of f.fields || []) {
      const clu = cluLower(r.clu_id);
      const k = clu || `${r.fsn}-${r.tract}-${r.field_id}`;
      const crop = r.crop || "—";
      const sk = k + "|" + (r.field_id || "") + "|" + crop + "|" + (r.type_code || "");
      if (seen.has(sk)) continue; seen.add(sk);
      let e = by.get(k);
      if (!e) { e = { k, clu, fsn: r.fsn, tract: r.tract, field: r.field_id, crops: new Map(), acres: 0, ppAc: 0, planted: null }; by.set(k, e); }
      const ac = Number(r.acres) || 0, pp = Number(r.pp_acres) || 0;
      e.acres += ac; e.ppAc += pp;
      const ck = crop + "|" + (r.type_code || "");
      const ce = e.crops.get(ck) || { crop, tc: r.type_code || null, acres: 0 };
      ce.acres += ac; e.crops.set(ck, ce);
      if (!e.planted && r.report_date) e.planted = r.report_date;
    }
    const items = [...by.values()].map((e) => {
      const crops = [...e.crops.values()].sort((a, b) => b.acres - a.acres);
      const name = labels[e.clu] || (jdNames[e.clu] || {}).name || null;
      const ftf = `${e.fsn || "—"}-${e.tract || "—"}-${e.field || "—"}`;
      return { ...e, crops, name, ftf, feat: (e.clu && featByClu.get(e.clu)) || null,
               ppFull: e.ppAc > 0 && e.acres - e.ppAc <= 0.05, ppPartial: e.ppAc > 0 && e.acres - e.ppAc > 0.05 };
    });
    items.sort(listSort === "name"
      ? (a, b) => String(a.name || "Field " + a.ftf).localeCompare(String(b.name || "Field " + b.ftf), undefined, { numeric: true })
      : (a, b) => (b.acres - a.acres) || a.ftf.localeCompare(b.ftf, undefined, { numeric: true }));
    return { mode: "fields", items, acres: items.reduce((a, e) => a + e.acres, 0) };
  }, [phoneOn, sheetEff, groups, rows, selectedFarmerId, labels, jdNames, featByClu, listSort]);
  const openFeat = (f) => {
    if (!f || !L) return;
    let center = null;
    try {
      center = f.geometry.type === "Point"
        ? L.latLng(f.geometry.coordinates[1], f.geometry.coordinates[0])
        : L.geoJSON({ type: "Feature", geometry: f.geometry }).getBounds().getCenter();
    } catch (e) { center = null; }
    setSelField({ p: f.properties, geom: f.geometry || null, center });
  };
  openFeatRef.current = openFeat;
  // Swipe the sheet up or down by its top edge; a tap on the handle folds it.
  const swipeRef = useRef(null);
  const swipedAtRef = useRef(0);
  const sheetGo = (dir) => {
    if (dir === "up") { setSheet(sheetEff === "min" ? "peek" : sheetEff === "peek" ? "list" : sheetEff); return; }
    if (sheetEff === "field") { setSelField(null); setSheet("peek"); return; }
    setSheet(sheetEff === "list" ? "peek" : "min");
  };
  const onSheetTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    const inScroll = e.target && e.target.closest && e.target.closest("[data-fm-scroll]");
    swipeRef.current = t && !inScroll ? t.clientY : null;
  };
  const onSheetTouchEnd = (e) => {
    const y0 = swipeRef.current; swipeRef.current = null;
    const t = e.changedTouches && e.changedTouches[0];
    if (y0 == null || !t) return;
    const dy = t.clientY - y0;
    if (Math.abs(dy) < 36) return;
    swipedAtRef.current = Date.now();
    sheetGo(dy < 0 ? "up" : "down");
  };
  const onHandleTap = () => {
    if (Date.now() - swipedAtRef.current < 450) return;
    if (sheetEff === "min") setSheet("peek");
    else if (sheetEff === "peek") setSheet("min");
    else sheetGo("down");
  };

  const [vh, setVh] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 900));
  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // A weather layer turns this screen into a radar, and a radar needs the screen.
  // On a phone the map was 420px with a wrapped crop legend (~120px) and the
  // scrubber (~90px) floating over it, leaving barely 200px of actual map — the
  // storm didn't fit in the window it was being watched through. When radar or the
  // totals overlay is on, the map takes most of the viewport instead.
  const weatherOn = showRadar || showPrecip;
  const mapH = phone ? phone.height : isMobile
    ? (weatherOn ? Math.max(420, Math.min(680, Math.round(vh - 190))) : 420)
    : Math.max(560, Math.min(1000, Math.round(vh - 300)));

  // Crop key: shown by default, and folded away by default once a weather layer is
  // on. Tracked as an explicit override rather than derived, so opening it during a
  // storm keeps it open instead of snapping shut on the next render.
  const [legendOpen, setLegendOpen] = useState(false);
  const legendCollapsed = weatherOn && !legendOpen;

  // Leaflet's attribution has to clear the floating legend — it's a licence
  // condition for the Esri imagery, so it can never be the thing that gets
  // covered. A fixed offset can't do it: the legend is a wrapping flex row whose
  // height depends on how many crops are in view and how wide the window is, so
  // it is one line on a desk monitor and two or three on a laptop. The previous
  // hardcoded 46px was sized for the one-line case and the attribution landed
  // straight through the swatches as soon as it wrapped.
  //
  // So measure the legend and let the attribution sit on top of whatever it
  // actually is. ResizeObserver catches the wrap changing without a window
  // resize event (opening a panel, filtering to fewer crops).
  const [legendNode, setLegendNode] = useState(null);
  const [legendH, setLegendH] = useState(0);
  useEffect(() => {
    if (!legendNode) { setLegendH(0); return; }
    const measure = () => setLegendH(legendNode.offsetHeight || 0);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(legendNode);
    return () => ro.disconnect();
  }, [legendNode]);

  // The weather bar (radar scrubber OR forecast readout) sits ABOVE the legend
  // and its height varies — the day strip alone roughly doubles it. Leaflet's
  // attribution was only being lifted by the legend, so it ended up printed
  // through the forecast cards. Measure the bar the same way and lift the
  // attribution clear of BOTH. Only one weather bar is ever shown at a time
  // (radar and totals are mutually exclusive), so one measurement covers it.
  const [weatherBarNode, setWeatherBarNode] = useState(null);
  const [weatherBarH, setWeatherBarH] = useState(0);
  useEffect(() => {
    if (!weatherBarNode) { setWeatherBarH(0); return; }
    const measure = () => setWeatherBarH(weatherBarNode.offsetHeight || 0);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(weatherBarNode);
    return () => ro.disconnect();
  }, [weatherBarNode]);

  // Leaflet caches the container size and only re-reads it when told to. Collapsing
  // a side panel or the Layers row changes that size without any window resize
  // event, so without this the tiles render into the OLD width and the rest of the
  // container stays blank — the map appears to stop halfway across. Two ticks
  // (immediate + settled) because the container is resized by flex rather than by
  // an explicit transition, so the final size isn't readable on the first frame.
  //
  // MUST live below railOpen / layersOpen / mapH. It was originally
  // placed next to the map-init effect ~850 lines above their declarations, so the
  // dependency array hit the temporal dead zone and the whole app crashed with
  // "Cannot access 'mapH' before initialization" — a dependency array is evaluated
  // where the useEffect CALL sits, not lazily.
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    const t1 = setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 0);
    const t2 = setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 220);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [railOpen, layersOpen, mapH, isMobile]);
  // ⚠ SCOPED TO THE FIELDS ON SCREEN, NOT THE BOOK. The Layers panel printed
  // "2592 fields with a harvest" above a map showing 161 — a book-wide count
  // reading as a description of what is drawn. This counts only what is drawn,
  // and carries each crop's own range because the ramp is per crop: two
  // identical greens on different crops are NOT the same yield.
  const yieldShown = useMemo(() => {
    if (paintBy !== "yield") return null;
    const by = {};
    let withY = 0;
    const feats = (fc && fc.features) || [];
    for (const f of feats) {
      const y = cluYield ? cluYield[String(f.properties.clu_id || "").toLowerCase()] : null;
      if (!y || !(y.value > 0)) continue;
      withY++;
      const c = String(y.crop || "").toUpperCase();
      (by[c] = by[c] || []).push(y.value);
    }
    const crops = Object.keys(by).sort().map((c) => {
      const v = by[c].slice().sort((a, b) => a - b);
      return { crop: c, lo: v[0], hi: v[v.length - 1], n: v.length };
    });
    return { total: feats.length, withY, crops };
  }, [paintBy, fc, cluYield]);

  // every crop in view — including the SECONDARY crop of a split-planted CLU, which
  // isn't the feature's dominant `crop`, so pull from the full crops[] breakdown.
  //
  // Keyed by (crop, type_code), NOT by crop alone. The swatch called fieldCropColor(c)
  // with no type code, so silage corn — which the MAP fills bronze — showed a grain-gold
  // chip in the legend, and untyped corn had no way to announce itself at all. A legend
  // that doesn't match the fill is worse than no legend. Corn only splits into separate
  // rows when more than one type is actually in view; a grain-only farm still reads "CORN".
  const cropsShown = useMemo(() => {
    const seen = new Map();
    for (const f of (fc?.features || [])) {
      for (const c of (f.properties.crops || [{ crop: f.properties.crop, type_code: f.properties.type_code }])) {
        const crop = (c.crop || "").toUpperCase();
        if (!crop) continue;
        const tc = crop === "CORN" ? String(c.type_code == null ? "" : c.type_code).trim() : "";
        const key = crop + "|" + tc;
        if (!seen.has(key)) seen.set(key, { key, crop, type_code: tc, color: fieldCropColor(crop, tc) });
      }
    }
    const all = [...seen.values()];
    const cornTypes = new Set(all.filter(e => e.crop === "CORN").map(e => e.type_code));
    return all.map(e => ({ ...e,
      label: e.crop === "CORN" && cornTypes.size > 1
        ? `CORN (${e.type_code === "026" ? "silage" : e.type_code === "016" ? "grain" : e.type_code ? `type ${e.type_code}` : "untyped"})`
        : e.crop,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [fc]);
  // Overlay toggles live in a wrapping bar ABOVE the map (not floating on it) so
  // they don't clutter the imagery — especially on mobile. Each is a chip that
  // tints when active.
  const overlayChip = (on) => ({ display:"inline-flex", alignItems:"center", gap:6, fontSize: phone ? 14 : 12.5, fontWeight:700, color:"#1f3320", cursor:"pointer", padding: phone ? "10px 12px" : "5px 9px", borderRadius: phone ? 10 : 8, border:"1px solid "+(on?"#bcd3bc":"#e3ebe3"), background: on?"#eef5ec":"#f8faf8", whiteSpace:"nowrap" });

  return (
    <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap: phone ? 0 : isMobile ? 10 : 8 }}>
      <div style={{ width: isMobile ? "auto" : (railOpen ? 280 : 0), flexShrink:0, overflow: railOpen ? undefined : "hidden",
                    // Closed means closed on a phone too. It used to render regardless
                    // of railOpen on mobile, stacked ABOVE the map — an agent got 233
                    // farm rows before the first pixel of map. The Fields control on the
                    // map opens it.
                    display: (!railOpen || phone) ? "none" : undefined,
                    background:"#fff", border:"1px solid #d9e4d9", borderRadius:10, padding:8, maxHeight:mapH, overflowY:"auto" }}>
        {/* This rail BROWSES fields; it does not select farms — that is the toolbar
            picker's one job. It kept its own filter box, an "All farms" button, a
            "Select shown" button and a checkbox per farm until 2026-08-13, which made
            it a second farm selector sitting next to the first with a different search
            behind it. Clicking a farm name here still scopes to that farm, but it
            writes the SAME selection the picker does. */}
        {!isPortal && <button onClick={exportShapefile} disabled={!fc || !fc.features.length || exporting}
          title="Download the mapped field boundaries (selected farm, or all in view) as a zipped shapefile"
          style={{ width:"100%", marginBottom:6, border:"1px solid #2d4a2d", borderRadius:6, padding:"7px", cursor: (!fc||!fc.features.length||exporting)?"default":"pointer", fontWeight:800, fontSize:12,
            background: (!fc||!fc.features.length||exporting) ? "#eef3ee" : "#1a2e1a", color: (!fc||!fc.features.length||exporting) ? "#677167" : "#a8d878" }}>
          {exporting ? "Exporting…" : "Export shapefile"}
        </button>}

        {/* Print maps lives in the toolbar chip row above the map, NOT in here — this
            rail is collapsed by default (railOpen starts false), so a button placed
            here is invisible until you happen to click "Fields". That is where I first
            put it, and it read as a missing feature. */}
        {farms.map(f => {
          const on = selSet.has(f.id);
          const open = openFarms.has(f.id) || farms.length === 1;   // one farm: show its fields
          let flds = [];
          if (open) {
            const seen = new Set();
            for (const fl of f.fields) { const k = (fl.clu_id||"") + "|" + (fl.field_id||"") + "|" + (fl.crop||""); if (seen.has(k)) continue; seen.add(k); flds.push(fl); }
            flds.sort((a,b)=> (a.fsn||"").localeCompare(b.fsn||"", undefined, {numeric:true}) || (a.field_id||"").localeCompare(b.field_id||"", undefined, {numeric:true}));
          }
          return (
            <div key={f.id}>
              <div style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 9px", borderRadius:7, fontSize:13.5,
                  background: on ? "#e3eede" : "transparent", color:"#1f3320" }}>
                <span onClick={()=>setOpenFarms(prev=>{ const n=new Set(prev); n.has(f.id)?n.delete(f.id):n.add(f.id); return n; })}
                  style={{ width:12, color:"#60735f", cursor:"pointer", textAlign:"center" }}>{open?"▾":"▸"}</span>
                <span onClick={()=>{ setOpenFarms(prev=>{ const n=new Set(prev); n.add(f.id); return n; }); if (!on) pickFarm(f.id); }}
                  title={on ? "Showing this farm" : "Show only this farm"}
                  style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", cursor:"pointer", fontWeight: on ? 800 : 400 }}>{f.name}</span>
                <span style={{ fontSize:11, color:"#677167", whiteSpace:"nowrap" }}>{Math.round(f.acres||0).toLocaleString()} ac · {f.fields.length}</span>
                {!mapped.has(f.id) && <span title="no boundary loaded yet" style={{ fontSize:10, color:"#bb7" }}>○</span>}
              </div>
              {open && (
                <div style={{ marginLeft:18, borderLeft:"1px solid #e3ebe3", paddingLeft:4 }}>
                  {flds.map((fl, i) => {
                    const hasGeo = !!(geoms && geoms[(fl.clu_id||"").toLowerCase()]);
                    const ppAc = fl.pp_acres!=null ? Number(fl.pp_acres) : 0;
                    const totAc = fl.acres!=null ? Number(fl.acres) : 0;
                    const plantedAc = Math.max(0, totAc - ppAc);
                    const ppFull = ppAc > 0 && plantedAc <= 0.05;
                    const ppPartial = ppAc > 0 && plantedAc > 0.05;
                    return (
                      <div key={i} onClick={()=>{ if (!on) pickFarm(f.id); if (hasGeo) setFocusClu((fl.clu_id||"").toLowerCase()); }}
                        title={`${hasGeo ? "Zoom to this field" : "No boundary loaded for this field"}${ppFull ? ` · PREVENT PLANT ${ppAc.toFixed(1)} ac` : ppPartial ? ` · ${plantedAc.toFixed(1)} planted + ${ppAc.toFixed(1)} PP` : fl.report_date ? " · Planted "+fl.report_date : ""}`}
                        style={{ display:"flex", alignItems:"center", gap:6, cursor: hasGeo ? "pointer" : "default", padding:"6px 7px", borderRadius:6, fontSize:12.5, color: hasGeo ? "#1f3320" : "#677167" }}>
                        <span style={{ width:9, height:9, borderRadius:2, display:"inline-block", flexShrink:0, opacity: hasGeo ? 1 : 0.35,
                          ...(ppFull
                            ? { border:"1px solid #d98a1f", backgroundImage:`repeating-linear-gradient(45deg, ${fieldCropColor(fl.crop, fl.type_code)} 0, ${fieldCropColor(fl.crop, fl.type_code)} 1.5px, #fff 1.5px, #fff 3px)` }
                            : { background: fieldCropColor(fl.crop, fl.type_code) }) }} />
                        {(() => {
                          const nm = labels[(fl.clu_id||"").toLowerCase()];
                          const ftf = `${fl.fsn||"—"}-${fl.tract||"—"}-${fl.field_id||"—"}`;
                          const ppMark = ppPartial ? <span title="Partial prevent plant" style={{ color:"#d98a1f", fontWeight:800, marginLeft:4 }}>⬚</span> : null;
                          return (
                            <span style={{ flex:1, minWidth:0, overflow:"hidden" }}>
                              {nm ? (<>
                                <span style={{ display:"block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:700 }}>{nm}{ppMark}</span>
                                <span style={{ display:"block", fontSize:10, color:"#677167", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ftf}</span>
                              </>) : (<span style={{ display:"block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ftf}{ppMark}</span>)}
                            </span>
                          );
                        })()}
                        {ppFull
                          ? <span style={{ color:"#9a5a1a", fontSize:11, fontWeight:700 }}>⬚ {ppAc.toFixed(0)} PP</span>
                          : ppPartial
                            ? <span style={{ fontSize:11 }}><span style={{ color:"#677167" }}>{plantedAc.toFixed(0)}</span> <span style={{ color:"#9a5a1a", fontWeight:700 }}>+{ppAc.toFixed(0)} PP</span></span>
                            : (fl.report_date && <span style={{ color:"#677167", fontSize:11 }}>{fl.report_date.slice(5).replace("-","/")}</span>)}
                        <span style={{ color:"#677167" }}>{fl.acres!=null ? Number(fl.acres).toFixed(0)+" ac" : "—"}</span>
                      </div>
                    );
                  })}
                  {flds.length===0 && <div style={{ fontSize:11, color:"#677167", padding:"3px 6px" }}>No fields.</div>}
                </div>
              )}
            </div>
          );
        })}
        {farms.length===0 && <div style={{ fontSize:12, color:"#677167", padding:"10px 4px" }}>No farms match.</div>}
      </div>

      <div style={{ flex:1, minWidth:0 }}>
        {/* Selected-farm stats. This was gated on `!inspOpen` — suppressed while a
            right-hand FieldInspector column showed the same rollup with more detail.
            That column is gone (FieldInspector has no render site at all), but
            `inspOpen` stayed `useState(true)` with nothing able to set it false, so
            the strip had quietly been dead on desktop and visible only on mobile.
            A guard on a variable whose partner no longer exists is not a guard. */}
        {selSet.size > 0 && !phone && (
          <div style={{ background:"#fff", border:"1px solid #d9e4d9", borderRadius:10, padding:"10px 14px", marginBottom:10, display:"flex", flexWrap:"wrap", gap:18, alignItems:"center" }}>
            {/* "Mapped acres" — this figure takes the CLU boundary's own measured
                acreage wherever the AIP reported none, so it is NOT the insured
                acreage, and it sits directly beside a revenue guarantee that IS
                derived from insured acres. Two different quantities under one word,
                one inch apart, was the misleading pair. */}
            {[["Fields", selStats.fields.toLocaleString()],
              ["Mapped acres", selStats.acres.toLocaleString(undefined, { maximumFractionDigits:1 })],
              ["Counties", String(selStats.counties)],
              [`${cropYear} Revenue Guar`, "$" + Math.round(selStats.revGuar).toLocaleString()],
            ].map(([l, v]) => (
              <div key={l}>
                <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", color:"#60735f" }}>{l}</div>
                <div style={{ fontSize:18, fontWeight:800, color:"#1a2e1a" }}>{v}</div>
              </div>
            ))}
            {Object.entries(selStats.byCrop).sort((a,b)=>b[1].bu-a[1].bu).map(([c, v]) => (
              <div key={c}>
                <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", color:"#60735f" }}>{c} bu guar</div>
                <div style={{ fontSize:18, fontWeight:800, color:"#1a2e1a" }}>{Math.round(v.bu).toLocaleString()}</div>
              </div>
            ))}
            {!guar && <div style={{ fontSize:11, color:"#677167" }}>loading guarantees…</div>}
          </div>
        )}
        {/* Print panel — full width under the toolbar so the slider has room. */}
        {printOpen && !phone && fc && fc.features.length > 0 && (
          <div style={{ marginBottom:8, padding:"10px 12px", borderRadius:T.r.md,
                        border:`1px solid ${T.line}`, background:T.surface }}>
            {/* Two documents off one control: the field maps, and the FSA planting
                report. They share the proximity grouping and the tile-gated print.
                A portal user gets the maps only — see printKindEff — so the picker
                is not drawn at all rather than drawn with one option. */}
            {!isPortal && (
            <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
              {[["maps", "Field maps"], ["fsa", "FSA planting report"]].map(([k, lbl]) => (
                <button key={k} onClick={() => setPrintKind(k)}
                  style={{ cursor:"pointer", font:"inherit", fontSize:12, fontWeight: printKind===k?800:600,
                           padding:"5px 12px", borderRadius:999,
                           border:`1px solid ${printKind===k ? "#2d4a2d" : T.line}`,
                           background: printKind===k ? "#2d4a2d" : T.surface,
                           color: printKind===k ? "#fff" : T.body }}>{lbl}</button>
              ))}
            </div>
            )}

            {printKindEff === "fsa" ? (
              !fsaFarmerId ? (
                <div style={{ fontSize:12, color:T.body }}>
                  Pick <b>one farm</b> above — an acreage report is filed per grower, so a book-wide one isn't a document anyone submits.
                </div>
              ) : fsaBusy ? (
                <div style={{ fontSize:12, color:T.faint }}>Reading planting records…</div>
              ) : !fsa ? null : fsa.error ? (
                <div style={{ fontSize:12, color:T.danger }}>Couldn't load: {String(fsa.error)}</div>
              ) : !fsa.rows.length ? (
                <div style={{ fontSize:12, color:T.body }}>No reported fields for this farm in {fsaYear}.</div>
              ) : (
                <div>
                  {/* CROP SELECT. Acreage is filed per crop and the FSA deadlines
                      differ, so this belongs on the document rather than being a
                      view convenience. Corn splits grain/silage — the 578 reports
                      them as separate crops. */}
                  {/* CROP YEAR. Offered from the years the machine data actually
                      covers, keyed by RMA crop year — so 2026 wheat appears under
                      2026 even though the planter ran in September 2025, which is
                      the year a grower would look for it under. */}
                  {((fsa && fsa.years) || []).length > 1 && (
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", marginBottom:9 }}>
                      <span style={{ fontSize:11, color:T.faint, fontWeight:700, marginRight:2 }}>CROP YEAR</span>
                      {fsa.years.map(y => {
                        const on = y.year === fsaYear;
                        return (
                          <button key={y.year} onClick={() => { setFsaYearSel(y.year); setFsaCrops(new Set()); }}
                            title={`${Math.round(y.acres).toLocaleString()} planted ac · ${y.crops.join(", ")}`}
                            style={{ cursor:"pointer", font:"inherit", fontSize:11.5, fontWeight: on ? 800 : 600,
                                     padding:"4px 11px", borderRadius:999,
                                     border:`1px solid ${on ? "#2d4a2d" : T.line}`,
                                     background: on ? "#2d4a2d" : T.surface, color: on ? "#fff" : T.body }}>
                            {y.year}
                            <span style={{ fontSize:10, opacity:.75, marginLeft:5 }}>{Math.round(y.acres).toLocaleString()}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {fsaCropOpts.length > 1 && (
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", marginBottom:9 }}>
                      <span style={{ fontSize:11, color:T.faint, fontWeight:700, marginRight:2 }}>CROP</span>
                      <button onClick={() => setFsaCrops(new Set())}
                        style={{ cursor:"pointer", font:"inherit", fontSize:11.5,
                                 fontWeight: fsaCrops.size ? 600 : 800, padding:"4px 11px", borderRadius:999,
                                 border:`1px solid ${fsaCrops.size ? T.line : "#2d4a2d"}`,
                                 background: fsaCrops.size ? T.surface : "#2d4a2d",
                                 color: fsaCrops.size ? T.body : "#fff" }}>All crops</button>
                      {fsaCropOpts.map(c => {
                        const on = fsaCrops.has(c.k);
                        return (
                          <button key={c.k} onClick={() => setFsaCrops(p => {
                            const n = new Set(p); n.has(c.k) ? n.delete(c.k) : n.add(c.k); return n; })}
                            title={`${c.fields} fields · ${Math.round(c.acres).toLocaleString()} planted ac`}
                            style={{ cursor:"pointer", font:"inherit", fontSize:11.5, fontWeight: on ? 800 : 600,
                                     padding:"4px 11px", borderRadius:999,
                                     border:`1px solid ${on ? "#2d4a2d" : T.line}`,
                                     background: on ? "#2d4a2d" : T.surface, color: on ? "#fff" : T.body,
                                     display:"inline-flex", alignItems:"center", gap:5 }}>
                            {c.k}
                            <span style={{ fontSize:10, opacity:.75 }}>{Math.round(c.acres).toLocaleString()}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {!fsaRows.length ? (
                    <div style={{ fontSize:12, color:T.body }}>No fields match the selected crop(s).</div>
                  ) : (
                  <>
                  <div style={{ display:"flex", gap:16, flexWrap:"wrap", fontSize:12, color:T.body, marginBottom:8 }}>
                    <span><b style={{ color:T.ink, fontSize:13 }}>{fsaSummary.planted_acres.toLocaleString()}</b> planted ac mapped</span>
                    <span><b style={{ color:T.ink }}>{fsaSummary.fields}</b> FSA fields</span>
                    {fsaSummary.unmapped_acres > 0.5 && (
                      <span style={{ color:"#8c3a3a" }}>
                        <b>{fsaSummary.unmapped_acres.toLocaleString()}</b> ac not mapped
                      </span>
                    )}
                    {fsaSummary.no_coverage_acres > 0.5 && (
                      <span style={{ color:"#8a6d1f" }}>
                        <b>{fsaSummary.no_coverage_acres.toLocaleString()}</b> ac no coverage map yet
                      </span>
                    )}
                  </div>

                  {/* HARD GATE. This document goes to a federal agency on the grower's
                      behalf, so anything questionable is acknowledged rather than
                      warned about — CLAUDE.md's own rule for the FSA path. */}
                  {fsaFlags.length > 0 && (
                    <div style={{ border:"1px solid #dcc48a", background:"#fdf7ea", borderRadius:T.r.sm,
                                  padding:"9px 11px", marginBottom:9 }}>
                      <div style={{ fontSize:11.5, fontWeight:800, color:"#7a5d12", marginBottom:5 }}>
                        Review before printing
                      </div>
                      <ul style={{ margin:0, paddingLeft:18, fontSize:11.5, color:T.body, lineHeight:1.55 }}>
                        {fsaFlags.map(f => <li key={f.k}>{f.msg}</li>)}
                      </ul>
                      <label style={{ display:"flex", alignItems:"center", gap:7, marginTop:7,
                                      fontSize:11.5, fontWeight:700, color:T.ink, cursor:"pointer" }}>
                        <input type="checkbox" checked={fsaAck} onChange={e => setFsaAck(e.target.checked)} />
                        I've reviewed these
                      </label>
                    </div>
                  )}

                  <button onClick={printFsaReport}
                    disabled={(fsaFlags.length > 0 && !fsaAck) || fsaPrinting}
                    title={fsaFlags.length > 0 && !fsaAck ? "Review the items above first" : "Print the planting report"}
                    style={{ border:"1px solid #2d4a2d", borderRadius:T.r.sm, padding:"7px 16px",
                             cursor:((fsaFlags.length>0 && !fsaAck)||fsaPrinting)?"not-allowed":"pointer", fontFamily:"inherit",
                             fontWeight:800, fontSize:12,
                             background:((fsaFlags.length>0 && !fsaAck)||fsaPrinting)?"#eef3ee":"#1a2e1a",
                             color:((fsaFlags.length>0 && !fsaAck)||fsaPrinting)?"#677167":"#a8d878" }}>
                    {fsaPrinting ? "Building maps…" : `Print ${fsaYear} planting report`}
                  </button>
                  <span style={{ fontSize:10.5, color:T.faint, marginLeft:10 }}>
                    acreage from the policy · machine acres as a cross-check
                  </span>
                  </>
                  )}
                </div>
              )
            ) : (
          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:14 }}>
            <div style={{ fontSize:12.5, fontWeight:800, color:T.ink, whiteSpace:"nowrap" }}>
              {printPages.length} page{printPages.length === 1 ? "" : "s"}
              <span style={{ fontWeight:500, color:T.faint }}> · {fc.features.length.toLocaleString()} fields · {printBiggest} on the fullest</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8, flex:"1 1 240px", minWidth:200 }}>
              <span style={{ fontSize:10.5, color:T.faint, whiteSpace:"nowrap" }}>tighter</span>
              <input type="range" min="0.5" max="8" step="0.5" value={printSpanMi}
                onChange={e => setPrintSpanMi(Number(e.target.value))}
                title="Ground covered by one page. Tighter = more pages, bigger fields on each."
                style={{ flex:1, accentColor:"#2d4a2d", cursor:"pointer" }} />
              <span style={{ fontSize:10.5, color:T.faint, whiteSpace:"nowrap" }}>wider</span>
              <span style={{ ...uiNum, fontSize:11.5, fontWeight:700, color:T.body, whiteSpace:"nowrap" }}>{printSpanMi} mi</span>
            </div>
            {/* Printing the whole book is ~hundreds of sheets and almost never what
                anyone means. Say so rather than discovering it at the printer. */}
            {printPages.length > 40 && (
              <div style={{ fontSize:11, color:"#8a6d1f", flex:"1 1 100%" }}>
                That's {printPages.length} sheets — pick a farm above, or widen the slider.
              </div>
            )}
            <button onClick={printFieldMaps}
              style={{ border:"1px solid #2d4a2d", borderRadius:T.r.sm, padding:"7px 16px", cursor:"pointer",
                       fontFamily:"inherit", fontWeight:800, fontSize:12, background:"#1a2e1a", color:"#a8d878",
                       whiteSpace:"nowrap" }}>
              Print {printPages.length} map{printPages.length === 1 ? "" : "s"}
            </button>
          </div>
            )}
          </div>
        )}
        <div style={{ position:"relative" }} className={phone ? "fm-phone" : undefined}>
          {/* Lift Leaflet's attribution clear of the floating legend. The offset is
              the legend's measured height plus its 8px inset and a 6px gap — see
              legendH above for why this can't be a constant. */}
          <style>{`.leaflet-bottom.leaflet-right .leaflet-control-attribution{margin-bottom:${(phone ? bIn + (legendH ? legendH + 14 : 6) : (legendH ? legendH + 14 : 46)) + (weatherBarH ? weatherBarH + 10 : 0)}px}`}</style>
          <div ref={mapEl} style={{ height:mapH, borderRadius: phone ? 0 : 10, overflow:"hidden", border: phone ? "none" : "1px solid #d9e4d9", background:"#dde6dd" }} />
        {/* Map chrome: floats ON the map, top-left beside the zoom buttons, the way
            map apps put their controls — it used to be a second toolbar row between the
            page toolbar and the map, which cost a row on every device. One slim row. Overlay controls hide behind "Layers" (with a
            live count of what's on) and the two side panels get toggles, so the map
            can take the full width when you want it. */}
        {L && !phone && (() => {
          const onCount = (showPrfGrids ? 1 : 0) + (showFarmerBounds ? 1 : 0) + (showParcels ? 1 : 0) + (showJdBounds ? 1 : 0) + (jdOpsOn ? 1 : 0) + (showRadar ? 1 : 0) + (showPrecip ? 1 : 0) + (showSoil ? 1 : 0);
          // One dark pill of controls on the imagery (the JD Operations Center pattern
          // Kevin pointed at): buttons are segments of a single bar, the active one lit.
          const chip = (active) => ({
            display:"inline-flex", alignItems:"center", gap:6, cursor:"pointer", fontFamily:"inherit",
            border:"none", borderRadius:9, padding: isMobile ? "7px 10px" : "7px 12px",
            fontSize:12.5, fontWeight:800, whiteSpace:"nowrap",
            background: active ? "#a8d878" : "transparent", color: active ? "#1a2e1a" : "#eef5ea",
          });
          return (
            <div style={{ position:"absolute", top:10, left:52, zIndex:1050, display:"flex", alignItems:"center", gap:2,
                          flexWrap: isMobile ? "nowrap" : "wrap", padding:3, borderRadius:12, background:"rgba(26,46,26,0.88)",
                          boxShadow:"0 4px 14px rgba(0,0,0,0.3)", backdropFilter:"blur(4px)",
                          maxWidth: isMobile ? "calc(100% - 104px)" : "calc(100% - 230px)" }}>
              {/* "Fields", not "Farms" — WHICH farm is chosen in the toolbar picker
                  above, on every tab. This rail is the field browser for it: expand a
                  farm, click a field to zoom, export the boundaries. It used to carry
                  a second farm selector (checkboxes + its own filter box) beside a
                  picker that did the same job. */}
              <button onClick={()=>setRailOpen(o=>!o)} title={railOpen ? "Hide the field list" : "Show the field list"}
                style={chip(railOpen)}>☰ Fields</button>
              <button onClick={()=>setLayersOpen(o=>!o)} style={chip(layersOpen || onCount > 0)}>
                Layers{onCount > 0 && <span style={{ ...uiNum, fontSize:10.5, fontWeight:800, padding:"1px 6px", borderRadius:9, background:"rgba(255,255,255,.25)" }}>{onCount}</span>}
                <span style={{ fontSize:10 }}>{layersOpen ? "▲" : "▼"}</span>
              </button>
              {/* PRINT MAPS — grouped by proximity, one page per group. Sits in this
                  always-visible row rather than the farm rail, which is collapsed by
                  default. The page COUNT is shown before printing and tracks the
                  tightness slider live, because "how many sheets is this" is the only
                  question you have standing at the printer and no mile figure answers it. */}
              <button onClick={()=>{ setPrintOpen(o=>!o); setLayersOpen(false); }}
                disabled={!fc || !fc.features.length}
                title="Print field maps grouped by location — fields close together share a page"
                style={{ ...chip(printOpen), cursor:(!fc||!fc.features.length)?"default":"pointer",
                         opacity:(!fc||!fc.features.length)?0.5:1 }}>
                {isMobile ? "Print" : "Print maps"}
                {printOpen && printPages.length > 0 && (
                  <span style={{ ...uiNum, fontSize:10.5, fontWeight:800, padding:"1px 6px", borderRadius:9,
                                 background:"rgba(255,255,255,.25)" }}>{printPages.length}</span>
                )}
                <span style={{ fontSize:10 }}>{printOpen ? "▲" : "▼"}</span>
              </button>
            </div>
          );
        })()}

          {/* THE FIELD CARD — what a tapped field opens (Kevin, 2026-10-17, pointing at
              JD Operations Center). A panel over the map's right edge on a desktop and
              a sheet along its bottom on a phone, so the map stays visible around it.
              Every figure is the one the popup carried, from the same properties; the
              popup's HTML-string buttons are real buttons here. */}
          {selField && !chopMode && !phone && (() => {
            const fp = selField.p || {};
            const fx = (n) => Number(n).toFixed(1);
            const title = fp.label || fp.jdName || ("Field " + (fp.field || "—"));
            const cropsOf = fp.multi ? (fp.crops || []) : [{ crop: fp.crop, acres: fp.acres, planted: fp.planted }];
            const acHead = fp.acres != null ? fx(fp.acres) + " ac insured"
              : fp.fieldAc != null ? fx(fp.fieldAc) + " ac from the field boundary" : "acres not reported per field";
            const isPRFf = String(fp.crop || "").toUpperCase() === "PRF" || (fp.crops || []).some(c => String(c.crop || "").toUpperCase() === "PRF");
            const hasJdF = !!(onOpenJdReports && fp.farmer_id && jdDataSet && jdDataSet.has(fp.farmer_id));
            const canJdMapF = !!(!isPortal && fp.farmer_id && jdDataSet && jdDataSet.has(fp.farmer_id));
            const canNameF = !!(onSaveLabel && fp.clu_id);
            const yRow = cluYield ? cluYield[String(fp.clu_id || "").toLowerCase()] : null;
            const pdStr = (d) => d ? d.slice(5, 7) + "/" + d.slice(8, 10) + "/" + d.slice(0, 4) : null;
            // The outline, drawn from the field's own geometry — longitude squeezed by
            // cos(latitude) so a square field is drawn square, not stretched.
            const shape = (() => {
              const g = selField.geom;
              if (!g || !g.coordinates) return null;
              const rings = g.type === "Polygon" ? [g.coordinates[0]] : g.type === "MultiPolygon" ? g.coordinates.map(pg => pg[0]) : [];
              const pts = rings.flat().filter(c => Array.isArray(c) && c.length >= 2);
              if (pts.length < 3) return null;
              const lat0 = pts.reduce((a, c) => a + c[1], 0) / pts.length;
              const kx = Math.cos(lat0 * Math.PI / 180);
              const xs = pts.map(c => c[0] * kx), ys = pts.map(c => -c[1]);
              const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
              const sc = 40 / Math.max(maxX - minX, maxY - minY, 1e-9);
              const d = rings.map(r => r.map((c, k) => (k ? "L" : "M") + ((c[0] * kx - minX) * sc + 2).toFixed(1) + " " + ((-c[1] - minY) * sc + 2).toFixed(1)).join(" ") + " Z").join(" ");
              return <svg width="44" height="44" viewBox="0 0 44 44" style={{ flexShrink: 0 }}>
                <path d={d} fill={fieldCropColor(fp.crop, fp.type_code)} fillOpacity="0.35" stroke={fieldCropColor(fp.crop, fp.type_code)} strokeWidth="1.6" strokeLinejoin="round" />
              </svg>;
            })();
            const btn = (primary) => ({ border: primary ? "none" : "1px solid #cdd9cd", background: primary ? "#1a2e1a" : "#fff",
              color: primary ? "#a8d878" : "#1a2e1a", borderRadius: 9, padding: "8px 12px", fontSize: 12.5, fontWeight: 800,
              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" });
            return (
              <div role="dialog" aria-label={"Field " + title}
                onKeyDown={(e) => { if (e.key === "Escape") setSelField(null); }}
                style={{ position: "absolute", zIndex: 1150, background: "#fff", boxShadow: "0 12px 34px -10px rgba(0,0,0,.45)",
                         border: "1px solid #d9e4d9", overflowY: "auto",
                         ...(isMobile
                           ? { left: 0, right: 0, bottom: 0, maxHeight: "62%", borderRadius: "16px 16px 10px 10px", padding: "10px 14px 14px" }
                           : { top: 56, right: 10, width: 330, maxHeight: "calc(100% - 72px)", borderRadius: 14, padding: "14px 16px" }) }}>
                {isMobile && <div style={{ width: 38, height: 4, borderRadius: 3, background: "#cdd9cd", margin: "0 auto 10px" }} />}
                <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                  {shape}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#1a2e1a", lineHeight: 1.2 }}>{title}</div>
                    <div style={{ fontSize: 12.5, color: "#54724f", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fp.farmer || ""}{fp.county ? " · " + String(fp.county).replace(/\b\w/g, c => c.toUpperCase()).replace(/\B\w/g, c => c.toLowerCase()) : ""}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", marginTop: 3 }}>{acHead}</div>
                  </div>
                  <button onClick={() => setSelField(null)} aria-label="Close"
                    style={{ border: "none", background: "#eef3ee", borderRadius: 999, width: 30, height: 30, cursor: "pointer",
                             fontSize: 15, color: "#2d4a2d", flexShrink: 0, fontFamily: "inherit" }}>✕</button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
                  {cropsOf.map((c, k) => (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: fieldCropColor(c.crop, fp.type_code), flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, color: "#1a2e1a" }}>
                        {fmCropName(c.crop)}{String(c.crop || "").toUpperCase() === "CORN" && fp.type_code ? (fp.type_code === "026" ? " · Silage" : fp.type_code === "016" ? " · Grain" : "") : ""}
                      </span>
                      <span style={{ color: "#60735f" }}>
                        {c.acres != null ? fx(c.acres) + " ac" : ""}{c.planted ? " · planted " + pdStr(c.planted) : ""}
                      </span>
                    </div>
                  ))}
                  {fp.multi && <div style={{ fontSize: 11.5, color: "#60735f" }}>Split-planted field · {fx(fp.acres || 0)} ac total</div>}
                  {fp.uninsurable ? (
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#5f6b78" }}>▨ Uninsurable · {fx(fp.uiAc || 0)} ac — no guarantee on this crop line</div>
                  ) : fp.ppFull ? (
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#9a6a00" }}>⬚ Prevent plant · whole field · {fx(fp.ppAc || 0)} ac</div>
                  ) : fp.ppPartial ? (
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#9a5a1a" }}>⬚ Partial prevent plant · {fx(fp.plantedAc || 0)} planted · {fx(fp.ppAc || 0)} PP</div>
                  ) : null}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 6, marginTop: 12 }}>
                  {[["Farm", fp.fsn], ["Tract", fp.tract], ["Field", fp.field]].map(([k, v]) => (
                    <div key={k} style={{ background: "#f4f8f3", borderRadius: 9, padding: "6px 9px" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: "#60735f" }}>{k}</div>
                      <div style={{ fontSize: 13.5, fontWeight: 800, color: "#1a2e1a" }}>{v || "—"}</div>
                    </div>
                  ))}
                </div>

                {(fp.jdName || (yRow && yRow.value > 0)) && (
                  <div style={{ marginTop: 10, fontSize: 12.5, color: "#1f5480", fontWeight: 700 }}>
                    {fp.jdName
                      ? <>{fp.jdName}<span style={{ color: "#60735f", fontWeight: 500 }}> · John Deere name</span></>
                      : <>{jdYieldLabel(yRow.crop, yRow.value, yRow.unit)}<span style={{ color: "#60735f", fontWeight: 500 }}> {yRow.crop || ""}{yRow.date ? " · " + String(yRow.date).slice(0, 4) : ""} · {jdHarvestWord(yRow.unit)}</span></>}
                  </div>
                )}

                <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 13 }}>
                  {onViewWeather && <button style={btn(true)} onClick={() => { setSelField(null); onViewWeather(fp); }}>Weather</button>}
                  {onViewPRF && isPRFf && <button style={btn(false)} onClick={() => { setSelField(null); onViewPRF(fp); }}>PRF rainfall</button>}
                  {hasJdF && <button style={btn(false)} onClick={() => { setSelField(null); onOpenJdReports(fp.farmer_id, fp.farmer, fp.jdFieldId || null, fp.jdName || fp.label || null, fp.crop || null); }}>Machine data</button>}
                  {canJdMapF && selField.center && (
                    <button style={btn(false)} onClick={() => {
                      const c = selField.center;
                      setSelField(null);
                      if (onSelectFarm) onSelectFarm(fp.farmer_id);
                      setShowJdBounds(true);
                      setJdOpsOn(false);
                      setJdWant({ lat: c.lat, lng: c.lng, name: fp.label || ("FSN " + (fp.fsn || "—") + " Fld " + (fp.field || "—")) });
                    }}>Yield map</button>
                  )}
                </div>

                {canNameF && (
                  <form onSubmit={(e) => { e.preventDefault(); const v = e.currentTarget.elements.fname.value; onSaveLabel(fp.clu_id, fp.farmer_id, v); setSelField(null); }}
                    style={{ display: "flex", gap: 6, marginTop: 12, paddingTop: 11, borderTop: "1px solid #e6ede6" }}>
                    <input name="fname" key={fp.clu_id} defaultValue={fp.label || ""} placeholder="Name this field…" maxLength={60}
                      style={{ flex: 1, minWidth: 0, padding: "7px 9px", border: "1px solid #cdd9cd", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} />
                    <button type="submit" style={btn(false)}>Save</button>
                  </form>
                )}
              </div>
            );
          })()}
          {/* Layers panel — floats OVER the map rather than stacking above it, so
              opening it costs no map height. Pinned top-left under Leaflet's zoom
              control; zIndex clears the tile pane but stays under the popups. */}
          {L && layersOpen && (
            <div data-fm-scroll={phone ? "1" : undefined}
                 style={phone
                   ? { position:"absolute", left:0, right:0, bottom:0, zIndex:1170, maxHeight:"72%", overflowY:"auto", WebkitOverflowScrolling:"touch",
                       overscrollBehavior:"contain", background:"#fff", borderRadius:"22px 22px 0 0", boxShadow:"0 -10px 30px rgba(0,0,0,.35)",
                       padding:"8px 16px 18px", boxSizing:"border-box" }
                   : { position:"absolute", top:50, left:52, zIndex:1100, maxWidth:"min(680px, calc(100% - 64px))",
                          maxHeight:"calc(100% - 62px)", overflowY:"auto",
                          boxShadow:"0 10px 30px -12px rgba(0,0,0,.45)", borderRadius:10 }}>
            {phone && (
              <>
                <div style={{ width:40, height:5, borderRadius:3, background:"#cdd9cd", margin:"0 auto 8px" }} />
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <div style={{ fontFamily:T.serif, fontSize:20, fontWeight:700, color:T.ink }}>Map layers</div>
                  <button onClick={() => { setLayersOpen(false); setSheet(showRadar || showPrecip ? "min" : "peek"); }}
                    style={{ height:40, padding:"0 16px", borderRadius:10, border:"none", background:"#eef3ee", color:T.ink, fontFamily:"inherit", fontSize:13.5, fontWeight:800, cursor:"pointer" }}>Done</button>
                </div>
              </>
            )}
            <div style={phone
                   ? { display:"flex", flexWrap:"wrap", alignItems:"center", gap:8 }
                   : { display:"flex", flexWrap:"wrap", alignItems:"center", gap:8, background:"#fff", border:"1px solid #d9e4d9", borderRadius:10, padding:"8px 10px", marginBottom:8 }}>
              <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"#60735f", marginRight:2 }}>Overlays</span>
              {/* PRF rainfall-index grid */}
              <label style={overlayChip(showPrfGrids)}>
                <input type="checkbox" checked={showPrfGrids} onChange={(e)=>setShowPrfGrids(e.target.checked)} style={{ cursor:"pointer" }} />
                PRF grids
              </label>
              {showPrfGrids && (
                <>
                  <label style={overlayChip(prfColorBy)}>
                    <input type="checkbox" checked={prfColorBy} onChange={(e)=>setPrfColorBy(e.target.checked)} style={{ cursor:"pointer" }} />
                    Color by rainfall
                  </label>
                  {prfColorBy && (
                    <>
                      <select value={prfIv} onChange={(e)=>setPrfIv(e.target.value)}
                        style={{ fontSize:12, padding:"4px 6px", borderRadius:6, border:"1px solid #cdd9cd", color:"#1f3320", background:"#f6faf6", cursor:"pointer" }}>
                        {WXP.ORDER.map(c => <option key={c} value={c}>{_PRF_LABEL[c] || c}</option>)}
                      </select>
                      {(() => { const ivOpenNow = !!prfIv && !prfIvStatus?.isFinal && PRF_IS.intervalStatus(prfIv, CURRENT_CROP_YEAR) !== "closed"; return (<>
                      {prfData && (prfIvStatus?.isFinal
                        ? <span style={{ fontSize:10, fontWeight:800, color:"#2d6a2d", background:"#e3f3e0", padding:"2px 6px", borderRadius:6 }}>Final</span>
                        : ivOpenNow
                        ? <span style={{ fontSize:10, fontWeight:800, color:"#5b6f8a", background:"#eef2f7", padding:"2px 6px", borderRadius:6 }}>Open · % of normal so far</span>
                        : <span style={{ fontSize:10, fontWeight:800, color:"#7a5a20", background:"#fbf2e0", padding:"2px 6px", borderRadius:6 }}>Estimate at close{prfIvStatus?.asOf ? ` · through ${prfIvStatus.asOf.slice(5).replace("-","/")}` : ""}</span>)}
                      <span style={{ display:"inline-flex", gap:5, alignItems:"center" }}>
                        {(ivOpenNow
                          ? [["<50",prfToDateColor(40)],["75",prfToDateColor(60)],["100",prfToDateColor(90)],["125",prfToDateColor(110)],["+",prfToDateColor(130)]]
                          : [["<70",prfIndexColor(60)],["85",prfIndexColor(80)],["100",prfIndexColor(95)],["115",prfIndexColor(110)],["+",prfIndexColor(130)]]).map(([lab,col])=>(
                          <span key={lab} style={{ display:"inline-flex", alignItems:"center", gap:2, fontSize:10.5, color:"#60735f" }}>
                            <span style={{ width:10, height:10, borderRadius:2, background:col, display:"inline-block", border:"1px solid #2d4a2d55" }} />{lab}
                          </span>
                        ))}
                      </span>
                      </>); })()}
                    </>
                  )}
                  <span style={{ fontSize:10, color:"#677167" }}>{!prfData ? "loading grids…" : "tap a cell for detail"}</span>
                </>
              )}
              {/* Farmer's own imported boundaries */}
              <label style={overlayChip(showFarmerBounds)}>
                <input type="checkbox" checked={showFarmerBounds} onChange={(e)=>setShowFarmerBounds(e.target.checked)} style={{ cursor:"pointer" }} />
                <span style={{ width:13, height:0, borderTop:"2px dashed #ff3db4", display:"inline-block" }} /> Farmer boundaries
              </label>
              {/* A grower can't import (the Import action is the agent's), and "our CLUs"
                  is our word for their fields. */}
              {showFarmerBounds && <span style={{ fontSize:10, color:"#677167" }}>{!farmerBounds ? "loading…"
                : farmerBounds.length ? (isPortal ? "drawn over your insured fields" : "over our CLUs")
                : (isPortal ? "none on file yet" : "none imported — use Import boundaries")}</span>}
              {/* NYS tax parcels — live landowner lookup */}
              <label style={overlayChip(showParcels)}>
                <input type="checkbox" checked={showParcels} onChange={(e)=>setShowParcels(e.target.checked)} style={{ cursor:"pointer" }} />
                <span style={{ width:12, height:12, borderRadius:2, background:"#2563eb22", border:"1.5px solid #2563eb", display:"inline-block" }} /> Landowners
              </label>
              {showParcels && (
                <span style={{ fontSize:10, color:"#677167" }}>
                  {(() => {
                    const st = parcels && parcels.status;
                    if (st === "zoom") return "zoom in to load";
                    if (st === "error") return "NYS unavailable";
                    if (st === "ok") return parcels.features.length ? `${parcels.features.length} parcels · tap for owner` : "none in view";
                    return "loading…";
                  })()} · NYS + county rolls
                </span>
              )}
              {/* ⚠ MACHINE YIELD LAYER. The count is printed because an empty layer
                  must say it is empty — a farm with no John Deere data would
                  otherwise paint entirely hollow and read as a bad year. */}
              <label style={overlayChip(paintBy === "yield")}>
                <input type="checkbox" checked={paintBy === "yield"}
                  onChange={(e)=>{ setPaintBy(e.target.checked ? "yield" : "crop"); }}
                  style={{ cursor:"pointer" }} />
                Machine yield
              </label>
              {paintBy === "yield" && (
                <span style={{ fontSize:10, color: yieldCount ? "#677167" : "#a5461b" }}>
                  {yieldCount
                    ? `${yieldCount} field${yieldCount === 1 ? "" : "s"} with a harvest · scaled within each crop`
                    : "no machine harvest on these fields — hollow means no data, not a low yield"}
                </span>
              )}
              {/* ⚠ ACCOUNTED-FOR LAYER. The count is printed for the same reason the yield
                  layer prints its own: a farm with no appraisals paints entirely hollow, and
                  without the count that reads as "every field is unaccounted for" rather than
                  "there is nothing to account them with". */}
              <label style={overlayChip(paintBy === "evidence")}>
                <input type="checkbox" checked={paintBy === "evidence"}
                  onChange={(e)=>{ setPaintBy(e.target.checked ? "evidence" : "crop"); }}
                  style={{ cursor:"pointer" }} />
                Accounted for
              </label>
              {paintBy === "evidence" && (
                <span style={{ fontSize:10, color: evidenceCount ? "#677167" : "#a5461b" }}>
                  {evidenceCount
                    ? `${evidenceCount} field${evidenceCount === 1 ? "" : "s"} with an appraisal or a combine pass · hollow is neither`
                    : "no appraisals or machine passes on these fields — hollow means nothing has spoken for them yet, not that they are empty"}
                </span>
              )}
              {/* Radar — observed through now, HRRR forward to +18h. Off by
                  default; the scrubber appears over the map once it's on. */}
              <label style={overlayChip(showRadar)}>
                <input type="checkbox" checked={showRadar}
                  onChange={(e)=>{ setShowRadar(e.target.checked); if (e.target.checked) setShowPrecip(false); }}
                  style={{ cursor:"pointer" }} />
                Radar
              </label>
              {showRadar && (
                <>
                  <span style={{ fontSize:10, color:"#677167" }}>
                    {!radarMan ? "loading frames…"
                      : `${radarMan.counts.observed} observed + ${radarMan.counts.forecast} forecast`
                        + (radarReady < radarMan.frames.length ? ` · caching ${radarReady}/${radarMan.frames.length}` : "")}
                  </span>
                  {/* One run vs the ensemble of recent runs. Forecast frames only
                      — observed radar is measured and has no ensemble. */}
                  <span style={{ display:"inline-flex", border:"1px solid #cdd9cd", borderRadius:6, overflow:"hidden" }}>
                    {[["single","1 run"],["prob","Ensemble"]].map(([k,lab]) => (
                      <button key={k} onClick={()=>setRadarMode(k)}
                        title={k==="single"
                          ? "One HRRR run, drawn sharp. Precise-looking; at long leads the precision isn't real."
                          : "Several recent runs valid at the same time, smoothed to the scale where they actually agree."}
                        style={{ cursor:"pointer", fontFamily:"inherit", border:"none", padding:"4px 9px",
                                 fontSize:10.5, fontWeight:800,
                                 background: radarMode===k ? "#2d4a2d" : "#fff",
                                 color: radarMode===k ? "#fff" : "#5a6a5a" }}>{lab}</button>
                    ))}
                  </span>
                  <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:10, color:"#60735f" }}>
                    opacity
                    <input type="range" min="20" max="100" step="5" value={Math.round(radarOpacity * 100)}
                      onChange={(e)=>setRadarOpacity(Number(e.target.value) / 100)}
                      style={{ width:70, cursor:"pointer" }} />
                  </span>
                </>
              )}
              {/* Forecast rain/snow totals — a window, not an instant. */}
              <label style={overlayChip(showPrecip)}>
                <input type="checkbox" checked={showPrecip}
                  onChange={(e)=>{ setShowPrecip(e.target.checked); if (e.target.checked) setShowRadar(false); }}
                  style={{ cursor:"pointer" }} />
                Forecasted totals
              </label>
              {showPrecip && (
                <>
                  <span style={{ display:"inline-flex", border:"1px solid #cdd9cd", borderRadius:6, overflow:"hidden" }}>
                    {[["rain","Rain"],["snow","Snow"]].map(([k,lab]) => (
                      <button key={k} onClick={()=>setPrecipField(k)}
                        style={{ cursor:"pointer", fontFamily:"inherit", border:"none", padding:"4px 10px",
                                 fontSize:10.5, fontWeight:800,
                                 background: precipField===k ? "#2d4a2d" : "#fff",
                                 color: precipField===k ? "#fff" : "#5a6a5a" }}>{lab}</button>
                    ))}
                  </span>
                  <span style={{ display:"inline-flex", border:"1px solid #cdd9cd", borderRadius:6, overflow:"hidden" }}>
                    {(precipWins ? precipWins.windows.map(w=>w.hours) : [6,12,24,48,72]).map((h) => (
                      <button key={h} onClick={()=>setPrecipHours(h)}
                        style={{ cursor:"pointer", fontFamily:"inherit", border:"none", padding:"4px 9px",
                                 fontSize:10.5, fontWeight:800,
                                 background: precipHours===h ? "#2d4a2d" : "#fff",
                                 color: precipHours===h ? "#fff" : "#5a6a5a" }}>{h}h</button>
                    ))}
                  </span>
                  {/* The REAL covered period, not "from now" — HRRR windows are
                      built by differencing run totals and land on hour boundaries. */}
                  <span style={{ ...uiNum, fontSize:10, color:"#60735f" }}>
                    {(() => {
                      const w = precipWins && precipWins.windows.find(x => x.hours === precipHours);
                      if (!w) return "loading…";
                      const f = (t) => new Date(t).toLocaleString("en-US", { weekday:"short", hour:"numeric", timeZone:"America/New_York" });
                      return `${f(w.from)} → ${f(w.to)}`;
                    })()}
                  </span>
                  <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:10, color:"#677167" }}>
                    in
                    {(precipField === "snow"
                      ? [["1","#b0d6f0"],["2","#80b6e4"],["4","#548ed4"],["8","#3e3c9c"],["18","#98246c"]]
                      : [["0.1","#7acc8c"],["0.25","#3ca85a"],["0.5","#f4e26e"],["1","#e8762c"],["2","#9c1a3e"]]
                    ).map(([lab,c]) => (
                      <span key={lab} style={{ display:"inline-flex", alignItems:"center", gap:2 }}>
                        <span style={{ width:9, height:9, borderRadius:2, background:c, display:"inline-block" }} />{lab}
                      </span>
                    ))}
                  </span>
                </>
              )}
              {/* John Deere Operations Center — field boundaries + sub-field yield heatmap.
                  Separate from our FSA/RMA CLU layer; show either or both together.
                  AGENT ONLY. "Compare operation" picks an OPERATION, and an
                  operation is another customer — its picker listed every
                  connected JD account by business name. The endpoint scopes to
                  the caller now, but an admin previewing the portal still holds
                  an admin token, so the API can't be the only guard: the whole
                  group is off on the portal side, in preview as well as live. */}
              {!isPortal && (<>
              {/* ── SOIL SAMPLES ──────────────────────────────────────────
                  Agent-only for now. The RLS is farmer-scoped and would hold, but
                  per the portal rule the SURFACE is hidden first and opened
                  deliberately — an admin previewing the portal still carries an
                  admin token, so the fetch alone is never the guard. */}
              {/* ONE question — "what am I looking at on these fields?" — so one
                  control. JD yield, JD planting, the whole-operation comparison and
                  every soil sampling are alternatives to each other, and two separate
                  checkboxes let them fight for the same map. Picking a soil sampling
                  reveals a SECOND selector for which analyte colours the zones. */}
              <span style={{ width:1, height:16, background:"#d0dcd0", margin:"0 2px" }} />
              <span style={{ fontSize:10.5, letterSpacing:"0.08em", color:"#60735f", fontWeight:800 }}>FIELD DATA</span>
              <select
                value={jdOpsOn ? "jdops" : showJdBounds ? "jd" : (showSoil && soilEventId) ? `soil:${soilEventId}` : ""}
                onChange={(e)=>{
                  const v = e.target.value;
                  // These are alternatives, so clear all of them before turning one on.
                  setShowJdBounds(false); setJdOpsOn(false); setJdSel(null); setJdOp(null);
                  setShowSoil(false); setSoilSel(null);
                  if (v === "jd") setShowJdBounds(true);
                  else if (v === "jdops") { setJdOpsOn(true); setShowJdBounds(true); }
                  else if (v.startsWith("soil:")) { setShowSoil(true); setSoilEventId(v.slice(5)); }
                }}
                title="Which data layer to draw on the fields"
                style={{ fontSize:12, fontWeight:700, padding:"4px 7px", borderRadius:6, border:"1px solid #cdd9cd", background:"#fff", color:"#2d4a2d", cursor:"pointer", maxWidth:260 }}>
                <option value="">none</option>
                <option value="jd">JD yield / planting</option>
                <option value="jdops">Compare operation</option>
                {soilEvents && soilEvents.length > 0 && (
                  <optgroup label="Soil samples">
                    {soilEvents.map(e => {
                      // centroid_lat is derived from the zones' own centroids, so it is
                      // non-null only when this sampling actually has geometry. A
                      // PDF-only sampling stays listed and says why it can't draw.
                      const mappable = e.samples ? e.samples.some(s => s.geometry) : e.centroid_lat != null;
                      return <option key={e.id} value={`soil:${e.id}`} disabled={!mappable}>
                        {`${String(e.sampled_on).slice(0,4)} · ${e.field_name || "field"} · ${e.sample_count||0} pts${mappable ? "" : " — no map"}`}
                      </option>;
                    })}
                  </optgroup>
                )}
              </select>
              {soilBusy && !soilEvents && <span style={{ fontSize:10, color:"#677167" }}>loading…</span>}
              {soilEvents && !soilEvents.length && selSet.size > 0 && (
                <span style={{ fontSize:10, color:"#677167" }}>no soil samples on file for this farm</span>
              )}
              {/* JD's own sub-controls. The layer is chosen in FIELD DATA above; what
                  follows only appears once it is on. */}
              {showJdBounds && (
                <input value={jdSearch} onChange={(e)=>setJdSearch(e.target.value)} onKeyDown={(e)=>{ if(e.key==="Enter") jumpToJdField(jdSearch); }}
                  list="jd-field-names" placeholder="Field name…" title="Find a JD field by name (Enter to jump)"
                  style={{ fontSize:12, padding:"4px 8px", borderRadius:6, border:"1px solid #cdd9cd", background:"#f6faf6", color:"#1f3320", width:118 }} />
              )}
              {showJdBounds && jdFields && (
                <datalist id="jd-field-names">{jdFields.features.map(f => <option key={f.properties.jd_field_id} value={f.properties.field} />)}</datalist>
              )}
              {showJdBounds && <span style={{ fontSize:10, color:"#677167" }}>{jdBusy ? "loading…" : (jdFields ? `${jdFields.features.length} fields · tap a field to pick a year` : "…")}</span>}
              {/* Whole-operation comparison — every harvest field of one crop-year on ONE fixed scale */}
              {jdOpsOn && jdOpsList && jdOpsList.length>1 && (
                <select value={jdOpsFarmer} onChange={(e)=>{ setJdOpsFarmer(e.target.value); setJdOpsSel({crop:"",year:""}); }}
                  style={{ fontSize:12, padding:"4px 6px", borderRadius:6, border:"1px solid #cdd9cd", background:"#f6faf6", color:"#1f3320", cursor:"pointer" }}>
                  {jdOpsList.map(o => <option key={o.farmer_id} value={o.farmer_id}>{o.name}</option>)}
                </select>
              )}
              {jdOpsOn && (
                <select value={jdOpsSel.crop && jdOpsSel.year ? `${jdOpsSel.crop}|${jdOpsSel.year}` : ""} onChange={(e)=>{ const [crop,year]=e.target.value.split("|"); setJdOpsSel({crop:crop||"",year:year||""}); }}
                  style={{ fontSize:12, fontWeight:700, padding:"4px 6px", borderRadius:6, border:"1px solid #cdd9cd", background:"#fff", color:"#2d4a2d", cursor:"pointer" }}>
                  <option value="">select crop / year…</option>
                  {(jdOpsManifest||[]).map(c => <option key={`${c.crop}|${c.year}`} value={`${c.crop}|${c.year}`}>{fmCropName(c.crop)} {c.year} · {c.fields} fld</option>)}
                </select>
              )}
              {jdOpsOn && jdOpsBusy && <span style={{ fontSize:10, color:"#677167" }}>loading maps…</span>}
              {jdOpsOn && jdOpsManifest && !jdOpsManifest.length && <span style={{ fontSize:10, color:"#677167" }}>no built harvest maps for this operation yet — grids still building</span>}
              {jdOpsOn && jdOpsData && (
                <span style={{ display:"inline-flex", gap:5, alignItems:"center", fontSize:10, color:"#60735f" }}>
                  <span style={{ fontWeight:700 }}>{jdOpsData.fields} fields · op avg {jdOpsData.mean}</span>
                  <input type="number" value={(jdOpsScale&&jdOpsScale.lo!=null)?jdOpsScale.lo:jdOpsData.lo}
                    onChange={(e)=>setJdOpsScale(s=>({ hi:(s&&s.hi!=null)?s.hi:jdOpsData.hi, lo:e.target.value===""?null:Number(e.target.value) }))}
                    title="scale low — dark red at/below this" style={{ width:44, fontSize:11, padding:"2px 4px", borderRadius:5, border:"1px solid #cdd9cd", textAlign:"right" }} />
                  <span style={{ display:"inline-flex", height:12, borderRadius:2, overflow:"hidden", border:"1px solid #2d4a2d33" }}>
                    {[0,0.14,0.28,0.42,0.57,0.71,0.85,1].map((t,i)=><span key={i} style={{ width:10, height:12, background:jdOpsColor(t,0,1), display:"inline-block" }} />)}
                  </span>
                  <input type="number" value={(jdOpsScale&&jdOpsScale.hi!=null)?jdOpsScale.hi:jdOpsData.hi}
                    onChange={(e)=>setJdOpsScale(s=>({ lo:(s&&s.lo!=null)?s.lo:jdOpsData.lo, hi:e.target.value===""?null:Number(e.target.value) }))}
                    title="scale high — purple (best spots) at/above this" style={{ width:44, fontSize:11, padding:"2px 4px", borderRadius:5, border:"1px solid #cdd9cd", textAlign:"right" }} />
                  <span>{jdShowUnit(jdOpsData.unit || "bu/ac")}</span>
                  {/* A calibrated map must never look like a raw one. The pattern is the
                      monitor's; the level is the grower's own total. */}
                  {(() => { const c = jdOpsData && jdCalFactor(jdOpsData.crop, jdOpsData.date || jdOpsData.year); return c ? (
                    <span title={`Monitor read ${c.monitor_yield} bu/ac; ${Number(c.total_bushels).toLocaleString()} bu actual works out to ${c.target_yield}. Every cell scaled x${(+c.factor).toFixed(3)}${c.coverage_pct != null ? ` · monitor covered ${c.coverage_pct}% of reported acres` : ""}.`}
                          style={{ background:"#eef6ec", border:"1px solid #cfe3cb", borderRadius:9, padding:"1px 7px", color:"#2d5a10", fontWeight:800, cursor:"help" }}>
                      CALIBRATED ×{(+c.factor).toFixed(2)}
                    </span>) : null; })()}
                  {jdOpsScale && <button onClick={()=>setJdOpsScale(null)} title="reset scale to operation 2–98%" style={{ fontSize:10, border:"1px solid #cdd9cd", borderRadius:5, background:"#f6faf6", cursor:"pointer", padding:"1px 5px", color:"#2d4a2d" }}>reset</button>}
                </span>
              )}
              {!jdOpsOn && jdOp && jdGrids[jdOp] && (() => {
                const gg = jdGrids[jdOp];
                const nn = (gg.values || []).filter(v => v != null).sort((a, b) => a - b);
                const dlo = nn.length ? +nn[Math.floor(nn.length * 0.02)].toFixed(0) : 0;
                const dhi = nn.length ? +nn[Math.floor(nn.length * 0.98)].toFixed(0) : 1;
                const lo = (jdFieldScale && jdFieldScale.lo != null) ? jdFieldScale.lo : dlo;
                const hi = (jdFieldScale && jdFieldScale.hi != null) ? jdFieldScale.hi : dhi;
                const isSeed = gg.metric === "seed_rate";
                return (
                  <span style={{ display:"inline-flex", gap:6, alignItems:"center", fontSize:10, color:"#60735f", flexWrap:"wrap" }}>
                    <span style={{ fontWeight:700, color:"#2d4a2d" }}>{gg.crop || ""} {gg.date ? String(gg.date).slice(0, 4) : ""} · {isSeed ? Math.round(gg.bu_mean).toLocaleString() : gg.bu_mean} {gg.unit || (isSeed ? "" : "bu/ac")} avg</span>
                    {gg.summary && (() => { const su = gg.summary;
                      // THE TILE ROW OPS CENTER SHOWS FOR A PASS, so a chopper's two figures sit side
                      // by side under the names JD gives them: "Yield" is the dry-matter tonnage and
                      // "Wet Weight" the as-chopped tonnage. Neither may print under the other's name,
                      // and a grain pass keeps its one bushel figure. (Kevin, 2026-09-09, on borrowing
                      // the Ops Center layout: the metric tiles were the half worth taking.)
                      const chop = su.wet_ton != null;
                      // Silage reads at 35% dry matter (Kevin, 2026-11-02); other chopped forage keeps its as-chopped wet tons.
                      const sil35 = chop && jdIsSilage(gg.crop) && su.dm_ton != null;
                      const tot = (!chop && su.area != null && su.yield != null) ? Math.round(su.area * su.yield) : null;
                      const tile = (label, val, unit, title) => (val == null || val === "" ? null :
                        <span key={label} title={title || ""} style={{ display:"inline-flex", flexDirection:"column", padding:"2px 7px", borderRadius:6, background:"#f4f8f4", border:"1px solid #dfe8df", lineHeight:1.15 }}>
                          <span style={{ fontSize:9.5, color:"#7a8f7a", textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</span>
                          <span style={{ fontSize:11, fontWeight:800, color:"#1a2e1a" }}>{val}{unit ? <span style={{ fontSize:10, fontWeight:600, color:"#60735f" }}> {unit}</span> : null}</span>
                        </span>);
                      return (
                      <span style={{ display:"inline-flex", gap:5, alignItems:"stretch", flexWrap:"wrap" }}>
                        {/* Wet tons lead, at 35% dry matter like every other screen (Kevin, 2026-11-02:
                            "I would adjust to 35% for silage"). DM follows; the as-chopped weight is JD's own. */}
                        {chop && sil35 && tile("Yield · wet", +(su.dm_ton / 0.35).toFixed(1), "ton/ac", "wet tons at 35% dry matter (65% moisture), the basis every Silas screen uses for silage")}
                        {chop && !sil35 && tile("Yield · wet", su.wet_ton, "ton/ac", "as-chopped tonnage")}
                        {chop && tile("Dry matter", su.dm_ton, "ton/ac", "Ops Center's Yield tile for a chopper — wet weight × (1 − moisture)")}
                        {chop && sil35 && tile("As chopped", su.wet_ton, "ton/ac", "John Deere's wet weight at this pass's own moisture")}
                        {!chop && !isSeed && su.yield != null && tile("Yield", su.yield, gg.unit || "bu/ac")}
                        {su.moisture != null && tile("Moisture", su.moisture.toFixed(1), "%")}
                        {su.area != null && tile(isSeed ? "Planted" : "Harvested", su.area.toFixed(1), "ac")}
                        {chop && su.area != null && su.dm_ton != null && tile("Total", `${Math.round(su.area * (sil35 ? su.dm_ton / 0.35 : su.wet_ton)).toLocaleString()} wet · ${Math.round(su.area * su.dm_ton).toLocaleString()} DM`, "ton")}
                        {tot != null && tile("Total", tot.toLocaleString(), gg.unit === "seeds/ac" ? "seeds" : "bu")}
                        {su.variety && tile("Variety", su.variety, "")}
                      </span>
                    ); })()}
                    <input type="number" value={lo} onChange={e => setJdFieldScale(s => ({ hi: (s && s.hi != null) ? s.hi : dhi, lo: e.target.value === "" ? null : Number(e.target.value) }))} title="scale low" style={{ width:44, fontSize:11, padding:"2px 4px", borderRadius:5, border:"1px solid #cdd9cd", textAlign:"right" }} />
                    <span style={{ display:"inline-flex", height:11, borderRadius:2, overflow:"hidden", border:"1px solid #2d4a2d33" }}>
                      {[0,0.2,0.4,0.6,0.8,1].map((t,i)=><span key={i} style={{ width:9, height:11, background:jdYieldColor(t,0,1), display:"inline-block" }} />)}
                    </span>
                    <input type="number" value={hi} onChange={e => setJdFieldScale(s => ({ lo: (s && s.lo != null) ? s.lo : dlo, hi: e.target.value === "" ? null : Number(e.target.value) }))} title="scale high" style={{ width:44, fontSize:11, padding:"2px 4px", borderRadius:5, border:"1px solid #cdd9cd", textAlign:"right" }} />
                    {jdFieldScale && <button onClick={() => setJdFieldScale(null)} title="reset scale" style={{ fontSize:10, border:"1px solid #cdd9cd", borderRadius:5, background:"#f6faf6", cursor:"pointer", padding:"1px 5px", color:"#2d4a2d" }}>reset</button>}
                    <label style={{ display:"inline-flex", alignItems:"center", gap:3, cursor:"pointer" }} title="Vector contour bands (John Deere-style, stays crisp at any zoom). Uncheck for raw raster pixels."><input type="checkbox" checked={jdContour} onChange={e => setJdContour(e.target.checked)} style={{ cursor:"pointer" }} />contour</label>
                  </span>
                );
              })()}
              </>)}
            </div>
            </div>
          )}
          {/* Loading overlay while the field boundaries fetch — otherwise the empty
              satellite map (no field polygons yet) reads as "broken." */}
          {!geoms && (
            <div style={{ position:"absolute", inset:0, zIndex:900, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(26,46,26,0.9)", color:"#a8d878", padding:"11px 18px", borderRadius:10, fontSize:13, fontWeight:800, boxShadow:"0 4px 16px rgba(0,0,0,0.35)" }}>
                <div style={{ width:16, height:16, border:"2.5px solid #a8d878", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
                {L ? "Loading field boundaries…" : "Loading map…"}
                <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
              </div>
            </div>
          )}
          {/* Area-measure control — top-right so it clears Leaflet's zoom buttons (top-left) */}
          {L && !(phoneBusy && !measuring) && (
            <div style={{ position:"absolute", top: phone ? topH + 136 : 10, right:10, zIndex:1000, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
              {!measuring ? (
                <button onClick={startMeasure} title="Measure the acreage of any area — click points on the map to trace a shape"
                  aria-label="Measure area"
                  style={{ background:"#1a2e1a", color:"#ffd34d", border:"none", borderRadius:isMobile ? 999 : 8, padding:isMobile ? "0 14px" : "7px 12px", minHeight:isMobile ? 40 : undefined, fontSize:isMobile ? 13 : 12.5, fontWeight:800, cursor:"pointer", boxShadow:"0 2px 8px rgba(0,0,0,0.25)", whiteSpace:"nowrap" }}>
                  {isMobile ? "Measure" : "Measure area"}
                </button>
              ) : (
                <div style={{ background:"#fff", border:"1px solid #d9e4d9", borderRadius:10, padding:10, boxShadow:"0 4px 16px rgba(0,0,0,0.22)", minWidth:178 }}>
                  <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", color:"#60735f" }}>Measured area</div>
                  <div style={{ fontSize:26, fontWeight:800, color:"#1a2e1a", lineHeight:1.1 }}>
                    {mpts.length >= 3 ? measureAcres.toLocaleString(undefined, { maximumFractionDigits:2 }) : "—"}
                    <span style={{ fontSize:13, fontWeight:700, color:"#60735f" }}> ac</span>
                  </div>
                  <div style={{ fontSize:11, color:"#60735f", margin:"2px 0 8px" }}>
                    {mpts.length === 0 ? "Click the map to start" : mpts.length < 3 ? `${mpts.length} point${mpts.length===1?"":"s"} · need 3+` : `${mpts.length} points`}
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={()=>setMpts(prev => prev.slice(0, -1))} disabled={!mpts.length} title="Remove the last point"
                      style={{ flex:1, border:"1px solid #cdd9cd", borderRadius:6, padding:"5px 0", fontSize:11.5, fontWeight:700, cursor: mpts.length?"pointer":"default", background:"#f6faf6", color: mpts.length?"#2d4a2d":"#bcc8bc" }}>↶ Undo</button>
                    <button onClick={()=>setMpts([])} disabled={!mpts.length} title="Clear all points"
                      style={{ flex:1, border:"1px solid #cdd9cd", borderRadius:6, padding:"5px 0", fontSize:11.5, fontWeight:700, cursor: mpts.length?"pointer":"default", background:"#f6faf6", color: mpts.length?"#2d4a2d":"#bcc8bc" }}>Clear</button>
                  </div>
                  <button onClick={stopMeasure} style={{ width:"100%", marginTop:6, border:"none", borderRadius:6, padding:"6px 0", fontSize:12, fontWeight:800, cursor:"pointer", background:"#1a2e1a", color:"#a8d878" }}>Done</button>
                </div>
              )}
            </div>
          )}
          {/* Hover readout — bu/ac at the cursor over a yield map. */}
          {jdHover && (
            <div style={{ position:"absolute", left:jdHover.x + 14, top:jdHover.y + 14, zIndex:1100, pointerEvents:"none", background:"rgba(26,46,26,0.92)", color:"#fff", padding:"3px 8px", borderRadius:6, fontSize:12, fontWeight:800, boxShadow:"0 2px 8px rgba(0,0,0,0.35)" }}>
              {jdHover.unit === "seeds/ac" ? jdHover.v.toLocaleString() : jdHover.v} {jdHover.unit}
              {jdHover.field && <span style={{ display:"block", fontSize:10, fontWeight:600, opacity:0.85 }}>{jdHover.field}</span>}
            </div>
          )}
          {/* John Deere field → year picker: click a JD field to choose which
              year's yield map (or see planting history) to render. */}
          {/* ── SOIL READING CONTROL, ON THE MAP ──────────────────────────────
              Which measurement is drawn is a question you ask WHILE looking at the
              map, so the control lives on the map — not inside a Layers panel you
              have to reopen. Choosing the layer stays in FIELD DATA above; choosing
              the number you read is here, with the picture and its scale. */}
          {showSoil && soilEventId && (() => {
            const ev = (soilEvents||[]).find(e => e.id === soilEventId); if (!ev) return null;
            const def = soilAnalyteDef(soilAnalyte);
            const zones = ev.samples ? ev.samples.filter(s => s.geometry) : null;
            const vals = zones ? zones.map(s => soilVal(s, soilAnalyte)).filter(Boolean) : [];
            const nums = vals.map(x => x.v).sort((a,b)=>a-b);
            const lo = nums.length ? (def.kind === "band" ? def.lo : nums[0]) : null;
            const hi = nums.length ? (def.kind === "band" ? def.hi : nums[nums.length-1]) : null;
            const derived = vals.filter(x => x.basis === "derived").length;
            const missing = zones ? zones.length - vals.length : 0;
            const unit = def.unit || (vals[0] && vals[0].unit !== "none" ? vals[0].unit : "");
            return (
              <div style={{ position:"absolute", left:10, bottom:10 + bIn, zIndex:1400, width:214,
                            background:"rgba(26,46,26,0.95)", color:"#e8f2e0", borderRadius:10,
                            padding:"10px 12px", boxShadow:"0 4px 18px rgba(0,0,0,0.42)", font:"12px/1.4 system-ui" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                  <div style={{ fontWeight:800, fontSize:12.5, color:"#a8d878" }}>
                    {ev.field_name || "Soil"} · {String(ev.sampled_on).slice(0,4)}
                  </div>
                  <span onClick={()=>{ setShowSoil(false); setSoilSel(null); }} title="Hide the soil layer"
                        style={{ cursor:"pointer", color:"#677167", fontWeight:800, lineHeight:1, fontSize:15 }}>×</span>
                </div>
                <select value={soilAnalyte} onChange={(e)=>setSoilAnalyte(e.target.value)}
                  title="Which measurement colours the zones"
                  style={{ width:"100%", marginTop:8, fontSize:11.5, fontWeight:700, padding:"6px 7px", borderRadius:6,
                           background:"#2d4a2d", color:"#cfe6bf", border:"1px solid #46603f", cursor:"pointer", boxSizing:"border-box" }}>
                  {SOIL_ANALYTES.map(a => <option key={a.key} value={a.key} style={{ background:"#1a2e1a" }}>{a.label}</option>)}
                </select>
                {!zones ? (
                  // "Not tested" is a claim about the LAB. Never say it mid-fetch.
                  <div style={{ fontSize:10.5, color:"#677167", marginTop:8 }}>loading zones…</div>
                ) : !zones.length ? (
                  <div style={{ fontSize:10.5, color:"#677167", marginTop:8 }}>this sampling has no mapped zones</div>
                ) : !nums.length ? (
                  <div style={{ fontSize:10.5, color:"#e0b84a", marginTop:8, lineHeight:1.35 }}>{def.label} was not tested on this sampling</div>
                ) : (
                  <>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:8 }}>
                      <span style={{ ...uiNum, fontSize:10, color:"#cfe6bf" }}>{lo.toFixed(def.dp)}</span>
                      <span style={{ display:"flex", flex:1, height:11, borderRadius:2, overflow:"hidden", border:"1px solid rgba(255,255,255,.25)" }}>
                        {[0,0.17,0.34,0.5,0.67,0.84,1].map((t,i)=>(
                          <span key={i} style={{ flex:1, background:soilColor(lo+t*(hi-lo), lo, hi, def.kind) }} />
                        ))}
                      </span>
                      <span style={{ ...uiNum, fontSize:10, color:"#cfe6bf" }}>{hi.toFixed(def.dp)}</span>
                    </div>
                    {unit && <div style={{ fontSize:10.5, color:"#8fae8f", marginTop:3, textAlign:"center" }}>{unit}</div>}
                    {/* Provenance rides with the number, on screen, not just in the DB. */}
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:7 }}>
                      {derived > 0 && (
                        <span title="Back-computed by the lab's export from CEC x base saturation, not measured by an extraction. Reads LOWER than the lab's own numbers — not a rate input."
                              style={{ background:"#3d3218", border:"1px solid #6b5a2a", borderRadius:9, padding:"1px 7px", fontSize:10.5, color:"#e0b84a", fontWeight:800, cursor:"help" }}>
                          DERIVED ×{derived}
                        </span>
                      )}
                      {missing > 0 && (
                        <span title="Zones where the lab did not run this measurement. Drawn grey — not as a zero."
                              style={{ fontSize:10.5, color:"#60735f", cursor:"help", alignSelf:"center" }}>{missing} not tested</span>
                      )}
                      {!ev.crs_confirmed && (
                        <span title="This shapefile shipped no .prj, so its coordinate system was inferred from the bounds rather than declared. Positions look right for New York but were never confirmed by the file."
                              style={{ background:"#3a1f1a", border:"1px solid #6e3a2e", borderRadius:9, padding:"1px 7px", fontSize:10.5, color:"#e08a72", fontWeight:800, cursor:"help" }}>
                          CRS UNCONFIRMED
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          {/* One sampled zone, every analyte the lab actually ran. Values that were
              derived rather than measured say so on the row — the provenance is not
              a footnote, it decides whether the number can carry a rate. */}
          {soilSel && (() => {
            const ev = (soilEvents||[]).find(e => e.id === soilSel.event_id);
            const rows = SOIL_ANALYTES.map(a => ({ a, r: soilVal(soilSel, a.key) })).filter(x => x.r);
            const untested = SOIL_ANALYTES.length - rows.length;
            return (
              <div style={{ position:"absolute", right:10, bottom:10 + bIn, zIndex:1400, width:250, background:"rgba(26,46,26,0.95)", color:"#e8f2e0", borderRadius:10, padding:"11px 13px", boxShadow:"0 4px 18px rgba(0,0,0,0.42)", font:"12px/1.4 system-ui", maxHeight:"56vh", overflowY:"auto" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                  <div style={{ fontWeight:800, fontSize:13, color:"#a8d878" }}>Zone {soilSel.sample_ref}{soilSel.acres!=null?` · ${Number(soilSel.acres).toFixed(2)} ac`:""}</div>
                  <span onClick={()=>setSoilSel(null)} title="Close" style={{ cursor:"pointer", color:"#677167", fontWeight:800, lineHeight:1, fontSize:15 }}>×</span>
                </div>
                {ev && (
                  <div style={{ fontSize:10, color:"#8fae8f", marginTop:3 }}>
                    {ev.field_name} · sampled {ev.sampled_on}{ev.lab ? ` · ${ev.lab}` : ""}
                    {ev.depth_in != null ? ` · ${ev.depth_in}" depth` : " · depth not reported"}
                  </div>
                )}
                <div style={{ marginTop:9, display:"flex", flexDirection:"column", gap:3 }}>
                  {rows.map(({ a, r }) => (
                    <div key={a.key} style={{ display:"flex", justifyContent:"space-between", gap:8, alignItems:"baseline",
                                              padding:"2px 0", borderBottom:"1px solid #2d4a2d" }}>
                      <span style={{ color: a.key === soilAnalyte ? "#cfe6bf" : "#677167", fontWeight: a.key === soilAnalyte ? 800 : 400 }}>{a.label}</span>
                      <span style={{ fontFamily:"ui-monospace,Menlo,monospace", color:"#e8f2e0", whiteSpace:"nowrap" }}>
                        {r.v.toFixed(a.dp)}{r.unit && r.unit !== "none" ? ` ${r.unit}` : ""}
                        {r.basis === "derived" && (
                          <span title={r.note || "derived, not measured"} style={{ color:"#e0b84a", marginLeft:4, cursor:"help" }}>~</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                {untested > 0 && (
                  <div style={{ fontSize:10, color:"#60735f", marginTop:7, lineHeight:1.35 }}>
                    {untested} analyte{untested===1?"":"s"} not run on this zone — omitted rather than shown as zero.
                  </div>
                )}
                {rows.some(x => x.r.basis === "derived") && (
                  <div style={{ fontSize:10, color:"#e0b84a", marginTop:6, lineHeight:1.35 }}>
                    ~ = back-computed by the lab's export from CEC × base saturation, not measured. Lower than the lab's own numbers; not a rate input.
                  </div>
                )}
                {ev && !ev.extraction && (
                  <div style={{ fontSize:10, color:"#8fae8f", marginTop:6, lineHeight:1.35 }}>
                    Extraction method not stated in this file — no Cornell conversion is possible until it's known.
                  </div>
                )}
              </div>
            );
          })()}
          {jdSel && (
            <div style={{ position:"absolute", left:10, bottom:10 + bIn, zIndex:1400, width:236, background:"rgba(26,46,26,0.95)", color:"#e8f2e0", borderRadius:10, padding:"11px 13px", boxShadow:"0 4px 18px rgba(0,0,0,0.42)", font:"12px/1.4 system-ui" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                <div style={{ fontWeight:800, fontSize:13, color:"#a8d878" }}>{jdSel.field || "Field"}{jdSel.acres!=null && Number.isFinite(Number(jdSel.acres)) ? ` · ${Number(jdSel.acres).toFixed(1)} ac` : ""}</div>
                <span onClick={()=>{ setJdSel(null); setJdOp(null); }} title="Close" style={{ cursor:"pointer", color:"#677167", fontWeight:800, lineHeight:1, fontSize:15 }}>×</span>
              </div>
              <div style={{ fontSize:10.5, letterSpacing:"0.08em", color:"#7f9a7f", margin:"8px 0 4px" }}>YIELD MAP</div>
              {(jdSel.harvests||[]).length ? (
                <select value={(jdSel.harvests||[]).some(h=>h.op_id===jdOp) ? jdOp : ""} onChange={e=>{ const h=(jdSel.harvests||[]).find(x=>String(x.op_id)===e.target.value); if(h) loadJdGrid(h, jdSel.jd_field_id); }}
                  style={{ width:"100%", fontSize:11.5, fontWeight:700, padding:"6px 7px", borderRadius:6, background:"#2d4a2d", color:"#cfe6bf", border:"1px solid #46603f", cursor:"pointer", boxSizing:"border-box" }}>
                  <option value="" disabled>select a year…</option>
                  {jdSel.harvests.map(h => <option key={h.op_id} value={h.op_id} style={{ background:"#1a2e1a" }}>{`${h.year||"?"} ${h.crop||""}${h.yield!=null?` · ${h.yield}`:""}${h.has_grid?" ✓":""}`}</option>)}
                </select>
              ) : <span style={{ color:"#677167", fontSize:11 }}>no harvest data</span>}
              {(jdSel.plantings||[]).filter(p=>p.op_id).length>0 && (
                <>
                  <div style={{ fontSize:10.5, letterSpacing:"0.08em", color:"#7f9a7f", margin:"9px 0 3px" }}>PLANTING MAP</div>
                  <select value={(jdSel.plantings||[]).some(p=>p.op_id===jdOp) ? jdOp : ""} onChange={e=>{ const p=(jdSel.plantings||[]).find(x=>String(x.op_id)===e.target.value); if(p&&p.op_id) loadJdGrid(p, jdSel.jd_field_id); }}
                    style={{ width:"100%", fontSize:11.5, fontWeight:700, padding:"6px 7px", borderRadius:6, background:"#2d4a2d", color:"#cfe6bf", border:"1px solid #46603f", cursor:"pointer", boxSizing:"border-box" }}>
                    <option value="" disabled>select a year…</option>
                    {jdSel.plantings.filter(p=>p.op_id).map(p => <option key={p.op_id} value={p.op_id} style={{ background:"#1a2e1a" }}>{`${p.year||"?"} ${p.crop||""}${p.has_grid?" ✓":""}`}</option>)}
                  </select>
                </>
              )}
              {/* COLOR SCALE, right where the map was picked. Kevin, 2026-10-18: "Did we
                  lose ability to edit color scale on yield maps?" — it lived only inside
                  the Layers panel, one closed popover away from the card you choose the
                  map in. Same state (jdFieldScale) as that control, so the two agree;
                  the defaults are the same 2nd–98th percentile of the loaded grid. */}
              {!jdOpsOn && jdOp && jdGrids[jdOp] && (() => {
                const nn = (jdGrids[jdOp].values || []).filter(v => v != null).sort((a, b) => a - b);
                const dlo = nn.length ? +nn[Math.floor(nn.length * 0.02)].toFixed(0) : 0;
                const dhi = nn.length ? +nn[Math.floor(nn.length * 0.98)].toFixed(0) : 1;
                const lo = (jdFieldScale && jdFieldScale.lo != null) ? jdFieldScale.lo : dlo;
                const hi = (jdFieldScale && jdFieldScale.hi != null) ? jdFieldScale.hi : dhi;
                const box = { width:56, fontSize:12, fontWeight:700, padding:"4px 5px", borderRadius:6, background:"#2d4a2d",
                              color:"#e8f2e0", border:"1px solid #46603f", textAlign:"center", boxSizing:"border-box", fontFamily:"inherit" };
                return (
                  <>
                    <div style={{ fontSize:10.5, letterSpacing:"0.08em", color:"#7f9a7f", margin:"9px 0 4px" }}>COLOR SCALE</div>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <input type="number" aria-label="Scale low" value={lo} style={box}
                        onChange={e => setJdFieldScale(s => ({ hi: (s && s.hi != null) ? s.hi : dhi, lo: e.target.value === "" ? null : Number(e.target.value) }))} />
                      <span style={{ flex:1, display:"inline-flex", height:10, borderRadius:3, overflow:"hidden" }}>
                        {[0,0.2,0.4,0.6,0.8,1].map((t,i)=><span key={i} style={{ flex:1, background:jdYieldColor(t,0,1) }} />)}
                      </span>
                      <input type="number" aria-label="Scale high" value={hi} style={box}
                        onChange={e => setJdFieldScale(s => ({ lo: (s && s.lo != null) ? s.lo : dlo, hi: e.target.value === "" ? null : Number(e.target.value) }))} />
                    </div>
                    {jdFieldScale && (
                      <button onClick={() => setJdFieldScale(null)}
                        style={{ marginTop:6, fontSize:11, fontWeight:700, border:"1px solid #46603f", borderRadius:6, background:"transparent",
                                 color:"#a8d878", cursor:"pointer", padding:"3px 9px", fontFamily:"inherit" }}>Reset to auto ({dlo}–{dhi})</button>
                    )}
                  </>
                );
              })()}
              {/* SOIL SAMPLES for this same field — the third data layer, so one panel
                  answers "what do I have on this field" for planting, harvest AND soil.
                  Matched by CONTAINMENT of the sample zones in this field's boundary,
                  not by name: a farmer's field name and a lab's field name rarely agree,
                  and a wrong join here would show one field's chemistry on another. */}
              {(() => {
                if (!soilEvents || !soilEvents.length) return null;
                const feat = (jdFields && jdFields.features || []).find(f => (f.properties||{}).jd_field_id === jdSel.jd_field_id);
                const rings = feat ? _fniPolys(feat.geometry) : [];
                // One point per event (stored on the event row) rather than all its
                // zones — the picker must work before any geometry has been fetched.
                const here = !rings.length ? [] : soilEvents.filter(ev =>
                  ev.centroid_lon != null &&
                  rings.some(poly => _fniPtIn(Number(ev.centroid_lon), Number(ev.centroid_lat), poly)));
                if (!here.length) return null;
                return (
                  <>
                    <div style={{ fontSize:10.5, letterSpacing:"0.08em", color:"#7f9a7f", margin:"9px 0 3px" }}>SOIL SAMPLES</div>
                    <select value={here.some(e => e.id === soilEventId) ? soilEventId : ""}
                      onChange={e => { const id = e.target.value; if (!id) return; setShowSoil(true); setSoilEventId(id); setSoilSel(null); }}
                      style={{ width:"100%", fontSize:11.5, fontWeight:700, padding:"6px 7px", borderRadius:6, background:"#2d4a2d", color:"#cfe6bf", border:"1px solid #46603f", cursor:"pointer", boxSizing:"border-box" }}>
                      <option value="" disabled>select a year…</option>
                      {here.map(ev => {
                        const mappable = ev.samples ? ev.samples.some(s => s.geometry) : ev.centroid_lat != null;
                        return <option key={ev.id} value={ev.id} disabled={!mappable} style={{ background:"#1a2e1a" }}>
                          {`${String(ev.sampled_on).slice(0,4)} ${ev.lab || ""} · ${ev.sample_count||0} pts${mappable ? " ✓" : " — no map"}`}
                        </option>;
                      })}
                    </select>
                  </>
                );
              })()}
              {jdMsg && <div style={{ fontSize:10.5, color: jdMsg.startsWith("✓") ? "#a8d878" : (jdMsg.startsWith("error")||jdMsg.startsWith("fetch failed") ? "#f08a6a" : "#e0b84a"), marginTop:8, lineHeight:1.35, wordBreak:"break-word" }}>{jdMsg}</div>}
              {jdLoading && <div style={{ fontSize:10, color:"#677167", marginTop:4, lineHeight:1.35 }}>First-time maps build from raw monitor data — a few minutes, then cached.</div>}
            </div>
          )}
          {/* Radar scrubber — the whole timeline in one strip: observed on the
              left, NOW, forecast on the right. Sits above the crop legend using
              its measured height (legendH) for the same reason the attribution
              does. Numbers only, no verdict text: the time, the source, and the
              run-to-run agreement are the data — the read is the user's. */}
          {showRadar && !phoneBusy && radarMan && radarMan.frames.length > 0 && (() => {
            const frames = radarMan.frames;
            const fr = frames[Math.min(radarIdx, frames.length - 1)];
            const nowIdx = Math.max(0, frames.findIndex((f) => f.isNow));
            const isFc = fr.kind === "forecast";
            // Agreement is computed at whole-hour RUN leads, but the scrubber
            // labels frames by time from NOW. Rounding the lead to the nearest
            // hour therefore printed a 2h-lead score against a "+30m" frame —
            // the number did not describe the picture. Only show it when a
            // computed lead is genuinely close to this frame.
            const agree = (() => {
              if (!isFc || !radarAgree) return null;
              const keys = Object.keys(radarAgree).map(Number);
              if (!keys.length) return null;
              const k = keys.reduce((a, b) => Math.abs(b - fr.leadMin) < Math.abs(a - fr.leadMin) ? b : a);
              return Math.abs(k - fr.leadMin) <= 30 ? radarAgree[k] : null;
            })();
            const d = new Date(fr.t);
            const clock = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
            const day = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" });
            const todayET = new Date().toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" });
            const deltaMin = Math.round((d - new Date(radarMan.nowFrame)) / 60000);
            const rel = deltaMin === 0 ? "now"
              : deltaMin > 0 ? `+${Math.floor(deltaMin / 60) ? `${Math.floor(deltaMin / 60)}h ` : ""}${deltaMin % 60 ? `${deltaMin % 60}m` : ""}`.trim()
              : `−${Math.floor(-deltaMin / 60) ? `${Math.floor(-deltaMin / 60)}h ` : ""}${-deltaMin % 60 ? `${-deltaMin % 60}m` : ""}`.trim();
            // Only the forecast frames are preloaded (observed are WMS tiles), so
            // readiness is measured against those. Comparing against every frame
            // left this false forever and the play button permanently disabled.
            const cached = radarReady >= fcFrameCount;
            // Ensemble readout for this frame: nearest SAMPLED lead (spread is
            // measured every 3h, not every frame).
            const spreadHere = (() => {
              if (!isFc || radarMode !== "prob" || !radarSpread) return null;
              const keys = Object.keys(radarSpread).map(Number);
              if (!keys.length) return null;
              const k = keys.reduce((a, b) => Math.abs(b - fr.leadMin) < Math.abs(a - fr.leadMin) ? b : a);
              return radarSpread[k];
            })();
            const usingEnsemble = radarMode === "prob" && isFc && fr.leadMin >= 120;
            // Staleness: how far behind the newest observed frame has fallen.
            // The refresh timer should keep this near zero; if it isn't, the
            // layer is showing the past as the present and must say so.
            const staleMin = Math.round((Date.now() - new Date(radarMan.nowFrame)) / 60000);
            return (
              <div ref={setWeatherBarNode} style={{ position:"absolute", left:8, right:8, bottom: phone ? bIn + (legendH ? legendH + 16 : 8) : (legendH ? legendH + 16 : 48), zIndex:950,
                            padding:"8px 11px", borderRadius:8, background:"rgba(255,255,255,0.95)",
                            border:"1px solid #d9e4d9", boxShadow:"0 6px 20px -10px rgba(0,0,0,.4)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                  <button onClick={()=>setRadarPlay(p=>!p)} disabled={!cached}
                    title={cached ? (radarPlay ? "Pause" : "Play the loop") : "Caching frames…"}
                    style={{ cursor: cached ? "pointer" : "default", fontFamily:"inherit", border:"1px solid #cdd9cd",
                             borderRadius:6, width:30, height:26, fontSize:12, fontWeight:800,
                             background: cached ? "#2d4a2d" : "#eef3ee", color: cached ? "#fff" : "#677167" }}>
                    {radarPlay ? "❚❚" : "▶"}
                  </button>
                  <button onClick={()=>{ setRadarPlay(false); setRadarIdx(nowIdx); }}
                    title="Jump back to the present"
                    style={{ cursor:"pointer", fontFamily:"inherit", border:`1px solid ${radarIdx===nowIdx ? "#2d4a2d" : "#cdd9cd"}`,
                             borderRadius:6, padding:"4px 9px", fontSize:11, fontWeight:800,
                             background: radarIdx===nowIdx ? "#2d4a2d" : "#fff", color: radarIdx===nowIdx ? "#fff" : "#3a4a3a" }}>
                    NOW
                  </button>
                  {/* Time + how far from now. Day shown only when it isn't today,
                      so an overnight forecast frame can't be misread as this afternoon. */}
                  <span style={{ ...uiNum, fontSize:15, fontWeight:800, color:"#1f3320", minWidth:74 }}>
                    {day !== todayET && <span style={{ fontSize:11, color:"#60735f", marginRight:4 }}>{day}</span>}
                    {clock}
                  </span>
                  <span style={{ ...uiNum, fontSize:11, fontWeight:700, color:"#60735f", minWidth:42 }}>{rel}</span>
                  {/* Source label — measured vs modelled, never blurred. */}
                  <span style={{ fontSize:10.5, fontWeight:800, letterSpacing:"0.05em", padding:"2px 7px", borderRadius:6,
                                 background: isFc ? (usingEnsemble ? "#eceaf8" : "#fbf2e0") : "#e3f3e0",
                                 color: isFc ? (usingEnsemble ? "#4a3a86" : "#7a5a20") : "#2d6a2d" }}>
                    {!isFc ? "OBSERVED RADAR" : usingEnsemble ? "ENSEMBLE" : "HRRR FORECAST"}
                  </span>
                  {/* Ensemble mode: member count and the MEASURED agreement scale.
                      "±30 km" is this run's own answer, not a constant — it widens
                      when the members scatter and tightens when they converge. */}
                  {usingEnsemble && spreadHere && (
                    <span style={{ fontSize:10, color:"#60735f" }}
                      title="Smallest radius at which ≥75% of the members agree over ≥50% of the echo area, measured from this run's own members. The picture is smoothed to that scale, so it can't be read more precisely than the models actually agree.">
                      <b style={{ ...uiNum, fontSize:12, color:"#4a3a86" }}>{spreadHere.members}</b> runs · agree to
                      <b style={{ ...uiNum, fontSize:12, color:"#4a3a86" }}> ±{spreadHere.radiusKm} km</b>
                      {spreadHere.reachedConsensus === false && (
                        <span style={{ color:"#a33", fontWeight:700 }}> · no consensus at any scale</span>
                      )}
                    </span>
                  )}
                  {usingEnsemble && !spreadHere && <span style={{ fontSize:10, color:"#677167" }}>measuring spread…</span>}
                  {isFc && !usingEnsemble && agree != null && (
                    <span style={{ fontSize:10, color:"#60735f" }}
                      title="Intersection-over-union of the ≥20 dBZ echo mask against the previous HRRR run at this same valid time. Run-to-run stability, not a verified hit rate.">
                      run agreement <b style={{ ...uiNum, color: agree >= 60 ? "#2d6a2d" : agree >= 35 ? "#7a5a20" : "#a33", fontSize:12 }}>{agree}%</b>
                    </span>
                  )}
                  {/* Arrival at the tapped point — a WINDOW across runs, never a
                      single instant. Timing is the error that actually decides
                      whether you get the hay in. */}
                  {radarProbe && radarArrival && (() => {
                    const base = new Date(radarArrival.base);
                    const fmt = (h) => new Date(base.getTime() + h * 3600000)
                      .toLocaleTimeString("en-US", { hour: "numeric", timeZone: "America/New_York" });
                    if (!radarProbe.runs) return (
                      <span style={{ fontSize:11, color:"#60735f" }}>
                        <b style={{ ...uiNum, color:"#3a4a3a" }}>0/{radarProbe.total}</b> runs bring ≥{radarArrival.threshold_dbz} dBZ here in {radarArrival.hours}h
                      </span>
                    );
                    const same = radarProbe.firstH === radarProbe.lastH;
                    return (
                      <span style={{ fontSize:11, color:"#60735f" }}
                        title="First hour each recent HRRR run reaches the threshold at this point. The spread is the timing disagreement between runs.">
                        arrives <b style={{ ...uiNum, fontSize:13, color:"#1f3320" }}>
                          {same ? fmt(radarProbe.firstH) : `${fmt(radarProbe.firstH)} – ${fmt(radarProbe.lastH)}`}
                        </b>
                        <span style={{ marginLeft:6 }}>
                          <b style={{ ...uiNum, color: radarProbe.runs === radarProbe.total ? "#2d6a2d" : "#7a5a20" }}>
                            {radarProbe.runs}/{radarProbe.total}</b> runs
                        </span>
                      </span>
                    );
                  })()}
                  {showRadar && !radarProbe && radarArrival && (
                    <span style={{ fontSize:10, color:"#677167" }}>tap the map for arrival time</span>
                  )}
                  {!cached && <span style={{ fontSize:10, color:"#677167" }}>caching {radarReady}/{fcFrameCount}</span>}
                  {staleMin >= 25 && (
                    <span style={{ fontSize:10.5, fontWeight:800, padding:"2px 7px", borderRadius:6, background:"#fdeceb", color:"#a33" }}
                      title="The newest frame is older than it should be — the refresh may have failed.">
                      STALE {staleMin}m
                    </span>
                  )}
                </div>
                {/* The track. Ticks are the frames themselves, so the observed →
                    forecast split and the 15-min/hourly cadence change are both
                    visible in the geometry rather than needing a caption.
                    On a phone 37 tick buttons are ~8px targets, so touch gets a
                    range input over a painted track instead. */}
                {isMobile ? (
                  <div style={{ marginTop:7 }}>
                    <div style={{ display:"flex", alignItems:"stretch", gap:1, height:12 }}>
                      {frames.map((f, i) => (
                        <div key={f.t} style={{ flex: f.kind==="forecast" && f.stepMin === 60 ? 4 : 1, minWidth:0, borderRadius:2,
                                                borderLeft: f.isNow ? "2px solid #1f3320" : "none",
                                                background: i === radarIdx ? "#1f3320" : f.kind==="forecast" ? "#e6c98a" : "#8fbf7f" }} />
                      ))}
                    </div>
                    <input type="range" min="0" max={frames.length - 1} step="1" value={radarIdx}
                      onChange={(e)=>{ setRadarPlay(false); setRadarIdx(Number(e.target.value)); }}
                      style={{ width:"100%", marginTop:2, cursor:"pointer" }} />
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"stretch", gap:1, marginTop:7, height:22 }}>
                    {frames.map((f, i) => {
                      const active = i === radarIdx;
                      const fc = f.kind === "forecast";
                      return (
                        <button key={f.t} onClick={()=>{ setRadarPlay(false); setRadarIdx(i); }}
                          title={`${new Date(f.t).toLocaleString("en-US", { weekday:"short", hour:"numeric", minute:"2-digit", timeZone:"America/New_York" })} · ${fc ? "HRRR forecast" : "observed"}`}
                          style={{ flex: fc && f.stepMin === 60 ? 4 : 1, minWidth:0, cursor:"pointer", padding:0,
                                   border:"none", borderRadius:2,
                                   borderLeft: f.isNow ? "2px solid #1f3320" : "none",
                                   background: active ? "#1f3320" : fc ? "#e6c98a" : "#8fbf7f",
                                   opacity: active ? 1 : 0.85 }} />
                      );
                    })}
                  </div>
                )}
                {/* Legend swaps with the mode: dBZ is not probability and the two
                    must never be read off the same key. */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4, fontSize:10, color:"#677167", gap:8, flexWrap:"wrap" }}>
                  {usingEnsemble ? (
                    <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                      % of runs
                      {[["15%","#bae0ec"],["30%","#84cae2"],["45%","#44aade"],["60%","#2274d6"],["75%","#2834be"],["90%","#5c0c92"]].map(([lab,c]) => (
                        <span key={lab} style={{ display:"inline-flex", alignItems:"center", gap:2 }}>
                          <span style={{ width:9, height:9, borderRadius:2, background:c, display:"inline-block" }} />{lab}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                      {/* The low end is IN the key now. It was missing while the map
                          painted from 5 dBZ, so most of what was on screen was a
                          colour the legend never explained — and it read as rain when
                          it was cloud or virga. Faded swatches match the faded pixels. */}
                      dBZ
                      {[["10","#019ff4",0.33],["15","#0300f4",0.33],["20","#02fd02",1],["35","#fdf802",1],
                        ["45","#fd9500",1],["55","#d40000",1],["65","#f800fd",1]].map(([lab,c,op]) => (
                        <span key={lab} style={{ display:"inline-flex", alignItems:"center", gap:2 }}>
                          <span style={{ width:9, height:9, borderRadius:2, background:c, opacity:op, display:"inline-block" }} />{lab}
                        </span>
                      ))}
                      <span style={{ color:"#677167" }}>· under 20 rarely reaches the ground</span>
                    </span>
                  )}
                  <span>{radarMan.run ? `HRRR ${radarMan.run.slice(8)}Z run` : ""}</span>
                </div>
              </div>
            );
          })()}
          {/* Totals readout — the measured number, not the colour.
              Driven by a TAP (sticky), which is the only interaction that exists
              on both a phone and a desktop; hover just updates a secondary live
              figure where a cursor exists. */}
          {showPrecip && !phoneBusy && (
            <div ref={setWeatherBarNode} style={{ position:"absolute", left:8, right:8, bottom: phone ? bIn + (legendH ? legendH + 16 : 8) : (legendH ? legendH + 16 : 48), zIndex:950,
                          padding:"8px 11px", borderRadius:8, background:"rgba(255,255,255,0.95)",
                          border:"1px solid #d9e4d9", boxShadow:"0 6px 20px -10px rgba(0,0,0,.4)",
                          display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
              <span style={{ fontSize:10.5, fontWeight:800, letterSpacing:"0.05em", padding:"2px 7px", borderRadius:6,
                             background:"#eef4fb", color:"#2b5580" }}
                title="National Blend of Models — the calibrated, bias-corrected NWS blend. Windows are whole 6-hour NBM buckets; the covered period is shown in the Layers bar.">
                {precipField === "snow" ? "SNOW" : "RAIN"} · {precipHours}H · NBM
              </span>
              {/* One reading, one place. Hover previews into this same slot on
                  desktop; a tap pins it. Shown as LIKELY plus the range across
                  runs — a lone figure reads as a promise. */}
              {(() => {
                const shown = (!isMobile && !precipProbe && precipHover) ? precipHover : precipProbe;
                if (!shown) return (
                  <span style={{ fontSize:12, color:"#60735f" }}>
                    {precipGrid ? "Tap the map for the forecast at that point" : "loading values…"}
                  </span>
                );
                const spread = shown.hi - shown.lo;
                return (
                  <>
                    <span style={{ display:"inline-flex", alignItems:"baseline", gap:5 }}>
                      <span style={{ ...uiNum, fontSize:24, fontWeight:800, color:"#1f3320", lineHeight:1 }}>
                        {shown.mid.toFixed(2)}
                      </span>
                      <span style={{ fontSize:11, fontWeight:700, color:"#60735f" }}>in likely</span>
                    </span>
                    <span style={{ display:"inline-flex", alignItems:"baseline", gap:5 }}
                      title="Lowest and highest total across the recent HRRR runs covering this same window. A wide spread means the runs disagree on how much falls here.">
                      <span style={{ ...uiNum, fontSize:15, fontWeight:700, color: spread > 0.25 ? "#7a5a20" : "#3a4a3a" }}>
                        {shown.lo.toFixed(2)}–{shown.hi.toFixed(2)}
                      </span>
                      <span style={{ fontSize:10.5, color:"#60735f" }}>
                        range{precipGrid && precipGrid.members ? ` · ${precipGrid.members} runs` : ""}
                      </span>
                    </span>
                    {precipProbe && (
                      <button onClick={()=>{ setPrecipProbe(null); setWatchName(null); setWatchSaved(false); }}
                        style={{ cursor:"pointer", fontFamily:"inherit", border:"1px solid #cdd9cd", borderRadius:6,
                                 padding:"3px 9px", fontSize:10.5, fontWeight:700, background:"#fff", color:"#5a6a5a" }}>
                        clear
                      </button>
                    )}
                    {/* Save this exact point to the Weather page. Agent-only: the table
                        is gated to agency staff, so never offer it in the portal. */}
                    {precipProbe && !isPortal && (
                      watchSaved ? (
                        <span style={{ fontSize:10.5, fontWeight:800, color:"#2d6a2d" }}>✓ saved to Weather</span>
                      ) : watchName == null ? (
                        <button onClick={()=>setWatchName("")}
                          title="Save this point to the Weather page"
                          style={{ cursor:"pointer", fontFamily:"inherit", border:"1px solid #cdd9cd", borderRadius:6,
                                   padding:"3px 9px", fontSize:10.5, fontWeight:700, background:"#fff", color:"#2d4a2d" }}>
                          + Weather
                        </button>
                      ) : (
                        <span style={{ display:"inline-flex", gap:5, alignItems:"center" }}>
                          <input autoFocus value={watchName} onChange={(e)=>setWatchName(e.target.value)}
                            onKeyDown={(e)=>{ if (e.key === "Escape") setWatchName(null); }}
                            placeholder="Name this spot"
                            style={{ fontFamily:"inherit", fontSize:11.5, padding:"4px 7px", borderRadius:6,
                                     border:"1px solid #cdd9cd", width:130 }} />
                          <button disabled={!watchName.trim()}
                            onClick={()=>{
                              const nm = watchName.trim(); if (!nm) return;
                              sbInsert("weather_locations", [{ name: nm, lat: precipProbe.lat, lon: precipProbe.lon }])
                                .then(()=>{ setWatchSaved(true); setWatchName(null); })
                                .catch(()=>setWatchName(null));
                            }}
                            style={{ cursor: watchName.trim() ? "pointer" : "default", fontFamily:"inherit", border:"none",
                                     borderRadius:6, padding:"4px 10px", fontSize:10.5, fontWeight:800,
                                     background: watchName.trim() ? "#2d6a2d" : "#cdd9cd", color:"#fff" }}>save</button>
                        </span>
                      )
                    )}
                  </>
                );
              })()}
              {precipGrid && (
                <span style={{ ...uiNum, fontSize:10, color:"#677167", marginLeft:"auto" }}>
                  max in view {precipGrid.maxIn.toFixed(2)}"
                </span>
              )}
              {/* What day is it going to rain, and how much — for the pinned point.
                  Chance and amount are shown SEPARATELY and never combined: the
                  amount is an expected value across the blend, so a 40% day can
                  legitimately show a trace. Multiplying them would be nonsense. */}
              {(precipDailyBusy || precipDaily) && (
                <div style={{ flexBasis:"100%", marginTop:6, paddingTop:7, borderTop:"1px solid #e6ede6" }}>
                  {precipDailyBusy && !precipDaily ? (
                    <span style={{ fontSize:11, color:"#677167" }}>reading the next few days…</span>
                  ) : (
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"stretch" }}>
                      {precipDaily.days.map((d) => {
                        const dt = new Date(`${d.date}T12:00:00Z`);
                        const wd = dt.toLocaleDateString("en-US", { weekday:"short", timeZone:"UTC" });
                        const md = dt.toLocaleDateString("en-US", { month:"numeric", day:"numeric", timeZone:"UTC" });
                        const pop = d.popPct;
                        // A day can be 40% likely and still average a trace. Say
                        // "trace" rather than print 0.00" next to a real chance —
                        // that pairing reads as a contradiction and erodes trust.
                        const amt = d.inches >= 0.01 ? `${d.inches.toFixed(2)}"`
                          : (pop != null && pop >= 20 ? "trace" : "—");
                        const wet = pop != null && pop >= 50;
                        return (
                          <div key={d.date} title={d.partial ? `Only ${d.hoursCovered}h of this day is inside the forecast window` : ""}
                            style={{ flex:"1 1 84px", minWidth:78, padding:"6px 8px", borderRadius:7,
                                     border:`1px solid ${wet ? "#bcd4ea" : "#e2e9e2"}`,
                                     background: wet ? "#f2f8fd" : "#fafcfa", opacity: d.partial ? 0.72 : 1 }}>
                            <div style={{ fontSize:10, fontWeight:800, color:"#3a4a3a" }}>
                              {wd} <span style={{ color:"#677167", fontWeight:600 }}>{md}</span>
                              {d.partial && <span style={{ color:"#677167", fontWeight:600 }}> ·{d.hoursCovered}h</span>}
                            </div>
                            {/* Amount leads, chance supports — matches the Weather page. */}
                            <div style={{ ...uiNum, fontSize:17, fontWeight:800, lineHeight:1.15,
                                          color: d.inches >= 0.10 ? "#1f5b8f" : d.inches >= 0.01 ? "#1f3320" : "#677167" }}>
                              {amt}
                            </div>
                            <div style={{ ...uiNum, fontSize:11, color:"#60735f" }}>
                              {pop == null ? "—" : `${pop}%`}
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ alignSelf:"center", fontSize:10, color:"#677167", maxWidth:150, lineHeight:1.3 }}>
                        chance of measurable rain · amount is the blend average, not the amount if it rains
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Crop legend — floats along the map's bottom edge instead of sitting
              below it, so it costs no map height. Translucent so imagery still reads
              through, and pointerEvents stay on for the tooltips. */}
          {/* On a phone the crop key is the sheet's crop bar; the floating key comes back
              only for the yield / accounted-for paints, which the bar can't explain. */}
          {(phone && (!L || !geoms || paintBy === "crop" || phoneBusy)) ? null : (!L || !geoms) ? (
            <div style={{ fontSize:12, color:"#60735f", padding:"6px 2px" }}>Loading satellite map…</div>
          ) : (
            /* zIndex sits BELOW the JD field panel (1400) and the layers panel (1100).
               As a bottom-anchored full-width strip it overlaps anything else anchored
               bottom-left — it was covering the Planting Map selector outright. The
               legend is reference; a panel you opened wins. A plain block comment,
               not {/* … *&#47;}: after `) : (` we're in expression position, where a
               brace starts an object literal. */
            /* The legend stays at bottom:8; it is the ATTRIBUTION that moves, by the
               measured height of this element (see legendH). Nudging the legend up
               instead does not work — the attribution had already been lifted by a
               fixed amount to clear it, so raising the legend just moved it into that
               same reclaimed space. Only one of the two can own the offset, and it has
               to be the attribution, because the legend's height is the variable. */
            /* COLLAPSED while a weather layer is up. Twelve crop swatches wrap to
               three or four lines on a phone and were eating a third of the map —
               and none of it is what you are looking at when you opened radar. It
               folds to one chip that still reports the field count, and reopens on
               tap, because the crop key is reference material and the storm is not. */
            legendCollapsed ? (
              <button ref={setLegendNode} onClick={() => setLegendOpen(true)}
                title="Show the crop colour key"
                style={{ position:"absolute", left:8, bottom:8 + bIn, zIndex:900, cursor:"pointer",
                         fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:7,
                         padding:"6px 11px", borderRadius:8, fontSize:11.5, fontWeight:700, color:"#2d4a2d",
                         background:"rgba(255,255,255,0.93)", border:"1px solid #d9e4d9",
                         boxShadow:"0 4px 14px -6px rgba(0,0,0,.35)" }}>
                {/* ⚠ THE SWATCHES FOLLOW THE FILL. Crop chips beside a yield-painted
                    map is the same mismatch as the open legend, in miniature. */}
                <span style={{ display:"inline-flex", gap:2 }}>
                  {(paintBy === "yield"
                    ? ["#d9534f", "#e8a33d", "#e8d54a", "#8fbc5a", "#1f5c2e"].map((c, i) => ({ key: "y" + i, color: c }))
                    : cropsShown.slice(0, 5)
                  ).map(e => (
                    <span key={e.key} style={{ width:9, height:9, borderRadius:2, background:e.color, display:"inline-block" }} />
                  ))}
                </span>
                {paintBy === "yield" && yieldShown
                  ? `${yieldShown.withY} of ${yieldShown.total} harvested`
                  : `${(fc?.features||[]).length} fields`}
                <span style={{ color:"#677167", fontWeight:800 }}>▴</span>
              </button>
            ) : (
            <div ref={setLegendNode}
                 style={{ position:"absolute", left:8, right:8, bottom:8 + bIn, zIndex:900,
                          display:"flex", flexWrap:"wrap", gap:10, alignItems:"center",
                          padding:"7px 11px", borderRadius:8,
                          background:"rgba(255,255,255,0.93)", border:"1px solid #d9e4d9",
                          boxShadow:"0 4px 14px -6px rgba(0,0,0,.35)" }}>
              {weatherOn && (
                <button onClick={() => setLegendOpen(false)} title="Collapse the crop key"
                  style={{ cursor:"pointer", fontFamily:"inherit", border:"none", background:"transparent",
                           padding:0, fontSize:12, fontWeight:800, color:"#677167" }}>▾</button>
              )}
              {/* ⚠ THE LEGEND FOLLOWS THE FILL. With the machine-yield layer on, the
                  map paints a red→green yield ramp; printing the crop key beside it
                  told the reader the colours meant something they did not. Same
                  defect this file already records against fieldCropColor. */}
              {paintBy === "evidence" ? (<>
                {/* ⚠ FOUR STATES, AND "nothing yet" IS DRAWN AS AN ABSENCE. It is hollow and
                    dashed in the legend exactly as it is on the map, because the one reading
                    that must not happen is a reader taking the pale fill for a measured low. */}
                <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, color:"#2d4a2d", fontWeight:700 }}>
                  Accounted for
                </span>
                {[
                  ["#1a4a7a", "appraised + combined — combine used", "Appraised and then combined for grain. The machine measured what came off; the appraisal is superseded."],
                  ["#d4a017", "appraised + chopped — appraisal used", "Appraised and then chopped on a grain policy (purple rim). The chopper's silage tonnage is not grain, so the appraisal is the figure."],
                  ["#d4a017", "appraised", "The adjuster valued standing crop. Solid where the appraisal names the field, dashed where it covers several fields on one farm number."],
                  ["#2d6a2d", "combined", "A machine harvest pass this crop year."],
                  ["#1f7a7a", "insured as silage, chopped", "Chopped on a silage policy. The John Deere chopper estimate is in tons and never counted as grain."],
                ].map(([c, label, title]) => (
                  <span key={label} title={title}
                    style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11.5, color:"#5c6f5c" }}>
                    <span style={{ width:12, height:12, borderRadius:3, background:c, display:"inline-block" }} />
                    {label}
                  </span>
                ))}
                <span title="Neither an appraisal nor a combine pass. Before harvest that means the combine has not been there yet; once the crop is off it is production nobody has stated."
                  style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11.5, color:"#60735f" }}>
                  <span style={{ width:12, height:12, borderRadius:3, display:"inline-block",
                    background:"#cfd8cf", border:"1px dashed #677167" }} />
                  nothing yet
                </span>
                <span title="Chopped with no appraisal on a grain policy. The silage tonnage is not a grain figure, so these acres have no production figure yet."
                  style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11.5, color:"#a33" }}>
                  <span style={{ width:12, height:12, borderRadius:3, display:"inline-block",
                    background:"#f3d6cf", border:"1px dashed #a33" }} />
                  chopped, not appraised
                </span>
              </>) : paintBy === "yield" ? (<>
                <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, color:"#2d4a2d", fontWeight:700 }}>
                  Machine yield
                  <span style={{ display:"inline-flex", gap:0 }}>
                    {["#d9534f","#e8a33d","#e8d54a","#8fbc5a","#3f8f3f","#1f5c2e"].map((c,i) => (
                      <span key={i} style={{ width:16, height:11, background:c, display:"inline-block",
                        borderTopLeftRadius:i===0?3:0, borderBottomLeftRadius:i===0?3:0,
                        borderTopRightRadius:i===5?3:0, borderBottomRightRadius:i===5?3:0 }} />
                    ))}
                  </span>
                  <span style={{ fontWeight:500, color:"#60735f" }}>low → high</span>
                </span>
                {/* ⚠ EACH CROP STATES ITS OWN RANGE. The ramp is scaled WITHIN a crop,
                    so a dark-green corn field and a dark-green soybean field are each
                    high for their own crop and are not comparable to one another. */}
                {(yieldShown ? yieldShown.crops : []).map(c => (
                  <span key={c.crop} style={{ fontSize:11.5, color:"#5c6f5c" }}
                        title="Scaled within this crop only — the same colour on another crop is not the same yield">
                    {c.crop.toLowerCase()} {Math.round(c.lo)}–{Math.round(c.hi)}
                  </span>
                ))}
                <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11.5, color:"#60735f" }}>
                  <span style={{ width:12, height:12, borderRadius:3, display:"inline-block",
                    border:"1px dashed #677167", background:"#cfd8cf" }} /> no machine data
                </span>
                <span style={{ marginLeft:"auto", fontSize:11, color: yieldShown && yieldShown.withY ? "#60735f" : "#a5461b" }}>
                  {yieldShown
                    ? `${yieldShown.withY} of ${yieldShown.total} fields harvested · scaled within each crop`
                    : "no machine harvest on these fields"}
                </span>
              </>) : (<>
              {cropsShown.map(e => (
                <span key={e.key} style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:"#2d4a2d" }}
                      title={e.crop === "CORN" && !e.type_code
                        ? "The AIP file carries no corn type on these acres — grain vs silage is not reported, not assumed"
                        : undefined}>
                  <span style={{ width:12, height:12, borderRadius:3, background: e.color, display:"inline-block" }} /> {e.label}
                </span>
              ))}
              {(fc?.features||[]).some(f=>f.properties.ppFull) && (
                <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:"#2d4a2d" }} title="Whole field prevent plant (AIP M13 feed — P27 fields under the coverage's prevent-plant unit)">
                  <span style={{ width:12, height:12, borderRadius:3, display:"inline-block", border:"1.4px dashed #60735f",
                    backgroundImage:"repeating-linear-gradient(45deg, #9aa85a 0, #9aa85a 2px, #fff 2px, #fff 4px)" }} /> Prevent Plant
                </span>
              )}
              {(fc?.features||[]).some(f=>f.properties.uninsurable) && (
                <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:"#2d4a2d" }}
                      title="Reported acres with no guarantee on this crop line (P11 non-premium acreage code). Usually they were rolled to another crop and are covered there — the field is still farmed.">
                  <span style={{ width:12, height:12, borderRadius:3, display:"inline-block", border:"1.4px dotted #5b6470",
                    background: UNINSURABLE_FILL }} /> Uninsurable
                </span>
              )}
              {(fc?.features||[]).some(f=>f.properties.multi) && (
                <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:"#2d4a2d" }} title="One CLU field split-planted across 2+ crops. We have acres per crop but not the internal boundary, so the field is striped in each crop's color (hover for the breakdown).">
                  <span style={{ width:12, height:12, borderRadius:3, display:"inline-block", border:"1px solid #fff",
                    backgroundImage:"repeating-linear-gradient(-45deg, #ffd21f 0, #ffd21f 4px, #54c24a 4px, #54c24a 8px)" }} /> Split-planted (2+ crops)
                </span>
              )}
              <span style={{ marginLeft:"auto", fontSize:11, color:"#60735f" }}>
                {(fc?.features||[]).length} fields mapped{selSet.size ? " · 1 farm" : ""}
              </span>
              </>)}
            </div>
            )
          )}
          {/* ── PHONE CHROME ─────────────────────────────────────────────────────
              Search, year and farm float along the top; Layers and Locate on the
              right; one sheet along the bottom. See the PHONE MODE notes above. */}
          {phone && (() => {
            const layerCount = (showPrfGrids ? 1 : 0) + (showFarmerBounds ? 1 : 0) + (showParcels ? 1 : 0) + (showJdBounds ? 1 : 0) + (jdOpsOn ? 1 : 0) + (showRadar ? 1 : 0) + (showPrecip ? 1 : 0) + (showSoil ? 1 : 0) + (paintBy !== "crop" ? 1 : 0);
            const fab = (bg) => ({ width: 48, height: 48, borderRadius: 14, border: "none", background: bg, cursor: "pointer", position: "relative",
              boxShadow: "0 6px 16px rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 });
            const bigBtn = (primary) => ({ height: 48, borderRadius: 12, border: primary ? "none" : "1px solid #cdd9cd",
              background: primary ? T.ink : "#fff", color: primary ? "#a8d878" : T.ink, fontFamily: "inherit", fontSize: 13.5, fontWeight: 800,
              cursor: "pointer", padding: "0 8px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, minWidth: 0,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" });
            const lbl = { fontSize: 10.5, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "#5f6f5e" };
            const fx1 = (n) => Number(n || 0).toFixed(1);
            const acTxt = (n) => Math.round(Number(n) || 0).toLocaleString();
            const closeBtn = (onClick, label) => (
              <button onClick={onClick} aria-label={label}
                style={{ border: "none", background: "#eef3ee", borderRadius: 999, width: 36, height: 36, cursor: "pointer",
                         fontSize: 15, color: "#2d4a2d", flexShrink: 0, fontFamily: "inherit" }}>✕</button>
            );
            const fieldName = (p) => p.label || p.jdName || `Field ${p.fsn || "—"}-${p.tract || "—"}-${p.field || "—"}`;
            // The list is the FARMS while more than one is on the map and none is picked
            // (see phoneList). The buttons that open it say so: "‹ All fields" that opened
            // a list of farms was a button lying about where it went.
            const listIsFarms = (groups || []).length > 1 && !selectedFarmerId;

            // ── sheet bodies ──
            const peekBody = (
              <div style={{ padding: "0 16px 14px" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 700, color: T.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{phone.title}</div>
                  <div style={{ fontSize: 12.5, color: "#4f6a4c", whiteSpace: "nowrap" }}>{phone.yearLabel}</div>
                </div>
                {!L && (
                  <div style={{ fontSize: 13, color: mapFail ? T.warn : "#4f6a4c", marginTop: 4 }}>
                    {mapFail ? "The satellite map didn't load. Check your connection and open Field Mapper again." : "Loading satellite map…"}
                  </div>
                )}
                {sheetEff === "peek" && phoneSum && (
                  <>
                    {phoneSum.fields === 0 ? (
                      <div style={{ fontSize: 13.5, color: "#4f6a4c", marginTop: 6, lineHeight: 1.45 }}>{phone.emptyText || "No fields match."}</div>
                    ) : (
                      <>
                        <div style={{ display: "flex", gap: 16, marginTop: 4, fontSize: 13, color: "#4f6a4c", flexWrap: "wrap" }}>
                          <span><b style={{ ...uiNum, color: T.ink, fontSize: 15 }}>{phoneSum.fields.toLocaleString()}</b> field{phoneSum.fields === 1 ? "" : "s"}</span>
                          {phoneSum.acres > 0 && <span><b style={{ ...uiNum, color: T.ink, fontSize: 15 }}>{acTxt(phoneSum.acres)}</b> acres</span>}
                          <span><b style={{ ...uiNum, color: T.ink, fontSize: 15 }}>{phoneSum.counties}</b> count{phoneSum.counties === 1 ? "y" : "ies"}</span>
                        </div>
                        {phoneSum.acres > 0 && phoneSum.crops.length > 0 && (
                          <>
                            <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginTop: 10, background: "#eef3ee" }}>
                              {phoneSum.crops.filter((c) => c.acres > 0).map((c) => (
                                <div key={c.k} title={c.name} style={{ width: `${(100 * c.acres) / phoneSum.acres}%`, background: c.color }} />
                              ))}
                            </div>
                            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 7, fontSize: 11.5, color: "#4f6a4c" }}>
                              {phoneSum.crops.slice(0, 4).map((c) => (
                                <span key={c.k} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                  <span style={{ width: 8, height: 8, borderRadius: 2, background: c.color }} />{c.name} {acTxt(c.acres)}
                                </span>
                              ))}
                              {phoneSum.crops.length > 4 && <span>+{phoneSum.crops.length - 4} more</span>}
                            </div>
                          </>
                        )}
                      </>
                    )}
                    {(() => {
                      const btns = [{ k: "fields", label: listIsFarms ? "Farms" : "Fields", primary: true, onClick: () => setSheet("list") }, ...(phone.views || [])];
                      const cols = btns.length <= 4 ? btns.length : 3;
                      return (
                        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 8, marginTop: 12 }}>
                          {btns.map((b) => (
                            <button key={b.k} onClick={b.onClick} style={bigBtn(!!b.primary)}>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{b.label}</span>
                              {b.badge > 0 && <span style={{ ...uiNum, fontSize: 10.5, fontWeight: 800, padding: "1px 6px", borderRadius: 9, background: "#fdf0e3", color: T.warn }}>{b.badge}</span>}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                    {phone.extra}
                  </>
                )}
              </div>
            );

            const listBody = phoneList && (
              <>
                <div style={{ padding: "0 16px", flexShrink: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 700, color: T.ink }}>
                        {phoneList.mode === "farms" ? `${phoneList.items.length.toLocaleString()} farms` : `${phoneList.items.length.toLocaleString()} field${phoneList.items.length === 1 ? "" : "s"}`}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#4f6a4c" }}>
                        {phoneList.acres > 0 ? `${acTxt(phoneList.acres)} ac · ` : ""}{phone.yearLabel}
                      </div>
                    </div>
                    {closeBtn(() => setSheet("peek"), "Close the list")}
                  </div>
                  {/* Shown while a crop filter is ON even if this farm grows one crop: a
                      filter picked on another farm must stay on screen to be cleared. */}
                  {phoneList.mode === "fields" && ((phone.crops || []).length > 1 || (phone.cropSet && phone.cropSet.size > 0)) && (
                    <div data-fm-scroll="1" style={{ display: "flex", gap: 6, marginTop: 10, overflowX: "auto", paddingBottom: 2 }}>
                      {[{ c: null, label: "All crops" }, ...[...new Set([...(phone.crops || []), ...(phone.cropSet ? [...phone.cropSet] : [])])].map((c) => ({ c, label: fmCropName(c) }))].map(({ c, label }) => {
                        const on = c == null ? !(phone.cropSet && phone.cropSet.size) : !!(phone.cropSet && phone.cropSet.has(c));
                        return (
                          <button key={c || "_all"} onClick={() => (c == null ? phone.onClearCrops && phone.onClearCrops() : phone.onToggleCrop && phone.onToggleCrop(c))}
                            style={{ height: 36, padding: "0 12px", borderRadius: 18, flexShrink: 0, fontFamily: "inherit", fontSize: 12.5, fontWeight: 800, cursor: "pointer",
                                     border: on ? "none" : "1px solid #cdd9cd", background: on ? T.ink : "#fff", color: on ? "#a8d878" : T.ink,
                                     display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {c != null && <span style={{ width: 10, height: 10, borderRadius: 3, background: fieldCropColor(c) }} />}{label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, paddingBottom: 8, borderBottom: "1px solid #e4ece4" }}>
                    <span style={lbl}>{phoneList.mode === "farms" ? "Pick a farm" : listSort === "name" ? "Sorted by name" : "Sorted by acres"}</span>
                    {phoneList.mode === "fields" && (
                      <button onClick={() => setListSort((s) => (s === "name" ? "acres" : "name"))}
                        style={{ height: 32, padding: "0 12px", borderRadius: 8, border: "none", background: "#eef3ee", color: T.ink, fontFamily: "inherit", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                        {listSort === "name" ? "Sort by acres" : "Sort by name"}
                      </button>
                    )}
                  </div>
                </div>
                <div data-fm-scroll="1" style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", padding: "0 16px 12px" }}>
                  {phoneList.mode === "farms" ? phoneList.items.map((f) => (
                    <button key={f.id} onClick={() => pickFarm(f.id)}
                      style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", minHeight: 56, padding: "8px 0",
                               border: "none", borderBottom: "1px solid #eef3ee", background: "none", cursor: "pointer", fontFamily: "inherit", color: T.ink }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {/* A plain green dot for "has John Deere data", the farm picker's own
                            mark, not a tractor emoji; and fields counted as fields — "field
                            rows" was our table's word, and one field planted to two crops is
                            still one field to the grower. */}
                        <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 15, fontWeight: 800, minWidth: 0 }}>
                          {(jdDataSet && jdDataSet.has(f.id)) && <span aria-label="Has John Deere data" title="Has John Deere data" style={{ width: 8, height: 8, borderRadius: 4, background: T.green, flexShrink: 0 }} />}
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                        </span>
                        <span style={{ display: "block", fontSize: 12, color: "#4f6a4c" }}>{(() => { const n = new Set((f.fields || []).map((r) => cluLower(r.clu_id) || `${r.fsn}-${r.tract}-${r.field_id}`)).size; return `${n.toLocaleString()} field${n === 1 ? "" : "s"}`; })()}{!mapped.has(f.id) ? " · no boundaries yet" : ""}</span>
                      </span>
                      <span style={{ textAlign: "right" }}>
                        <span style={{ ...uiNum, display: "block", fontSize: 15, fontWeight: 800 }}>{acTxt(f.acres)}</span>
                        <span style={{ display: "block", fontSize: 11, color: "#4f6a4c" }}>acres</span>
                      </span>
                      <span style={{ color: "#9aa89a", fontSize: 20, lineHeight: 1 }}>›</span>
                    </button>
                  )) : phoneList.items.map((e) => {
                    const c0 = e.crops[0] || {};
                    const color = fieldCropColor(c0.crop, c0.tc);
                    const tappable = !!e.feat;
                    const cropTxt = e.crops.map((c) => fmCropName(c.crop, c.tc)).join(" + ");
                    const sub = [cropTxt, e.planted && fmMd(e.planted) ? "planted " + fmMd(e.planted) : null, e.name ? e.ftf : null,
                                 !e.feat ? "no boundary on file" : e.feat.geometry && e.feat.geometry.type === "Point" ? "location only" : null].filter(Boolean).join(" · ");
                    return (
                      <button key={e.k} onClick={() => tappable && openFeat(e.feat)} disabled={!tappable}
                        style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", minHeight: 60, padding: "9px 0",
                                 border: "none", borderBottom: "1px solid #eef3ee", background: "none", cursor: tappable ? "pointer" : "default",
                                 fontFamily: "inherit", color: T.ink, opacity: 1 }}>
                        <FmOutline geom={e.feat ? e.feat.geometry : null} size={40} color={color} dashed={e.ppFull} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 15, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name || "Field " + e.ftf}</span>
                          <span style={{ display: "block", fontSize: 12, color: "#4f6a4c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>
                          {(e.ppFull || e.ppPartial) && (
                            <span style={{ display: "inline-block", marginTop: 4, fontSize: 11, fontWeight: 800, color: "#8a5a00", background: "#fdf3df", borderRadius: 6, padding: "2px 7px" }}>
                              {e.ppFull ? "Prevent plant · whole field" : `Prevent plant · ${fx1(e.ppAc)} ac`}
                            </span>
                          )}
                        </span>
                        <span style={{ textAlign: "right", flexShrink: 0 }}>
                          <span style={{ ...uiNum, display: "block", fontSize: 15, fontWeight: 800 }}>{e.acres > 0 ? fx1(e.acres) : "—"}</span>
                          <span style={{ display: "block", fontSize: 11, color: "#4f6a4c" }}>acres</span>
                        </span>
                      </button>
                    );
                  })}
                  {phoneList.items.length === 0 && <div style={{ fontSize: 13, color: "#4f6a4c", padding: "16px 0" }}>{phone.emptyText || "No fields match."}</div>}
                </div>
              </>
            );

            const fieldBody = sheetEff === "field" && selField && (() => {
              const fp = selField.p || {};
              const title = fieldName(fp);
              const cropsOf = fp.multi ? (fp.crops || []) : [{ crop: fp.crop, acres: fp.acres, planted: fp.planted, type_code: fp.type_code }];
              const acHead = fp.acres != null ? fx1(fp.acres) + " ac insured"
                : fp.fieldAc != null ? fx1(fp.fieldAc) + " ac from the boundary" : "acres not reported per field";
              const isPRFf = String(fp.crop || "").toUpperCase() === "PRF" || (fp.crops || []).some((c) => String(c.crop || "").toUpperCase() === "PRF");
              const hasJdF = !!(onOpenJdReports && fp.farmer_id && jdDataSet && jdDataSet.has(fp.farmer_id));
              const canJdMapF = !!(!isPortal && fp.farmer_id && jdDataSet && jdDataSet.has(fp.farmer_id) && selField.center);
              const canNameF = !!(onSaveLabel && fp.clu_id);
              const yRow = cluYield ? cluYield[cluLower(fp.clu_id)] : null;
              // A pasture nobody combined has no yield map to open.
              const ymOk = canJdMapF && (!isPRFf || !!(yRow && yRow.value > 0));
              const sub = [cropsOf.length === 1 ? fmCropName(cropsOf[0].crop, cropsOf[0].type_code || fp.type_code) : `${cropsOf.length} crops`, acHead,
                           cropsOf.length === 1 && fmMd(cropsOf[0].planted) ? "planted " + fmMd(cropsOf[0].planted) : null].filter(Boolean).join(" · ");
              const who = [farms.length > 1 || !isPortal ? fp.farmer : null, fp.county ? fmTitleCase(fp.county) + " County" : null].filter(Boolean).join(" · ");
              const acts = [
                // On a PRF field the PRF page is the one that matters, so it leads.
                onViewPRF && isPRFf && { k: "prf", label: "PRF rainfall", onClick: () => onViewPRF(fp) },
                hasJdF && { k: "jd", label: "Machine data", onClick: () => { onOpenJdReports(fp.farmer_id, fp.farmer, fp.jdFieldId || null, fp.jdName || fp.label || null, fp.crop || null, title); } },
                ymOk && { k: "ym", label: "Yield map", onClick: () => {
                  const c = selField.center;
                  setSelField(null); setSheet("min");
                  if (onSelectFarm) onSelectFarm(fp.farmer_id);
                  setShowJdBounds(true); setJdOpsOn(false);
                  setJdWant({ lat: c.lat, lng: c.lng, name: fp.label || ("FSN " + (fp.fsn || "—") + " Fld " + (fp.field || "—")) });
                } },
                onViewWeather && { k: "wx", label: "Weather", onClick: () => onViewWeather(fp) },
                canNameF && { k: "nm", label: fp.label ? "Rename field" : "Name field", onClick: () => setRenaming((r) => !r) },
              ].filter(Boolean);
              return (
                <div data-fm-scroll="1" style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", padding: "0 16px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <button onClick={() => { setSelField(null); setSheet("list"); }}
                      style={{ height: 40, border: "none", background: "none", padding: 0, fontFamily: "inherit", fontSize: 13.5, fontWeight: 800, color: T.green, cursor: "pointer" }}>{listIsFarms ? "‹ Farms" : "‹ All fields"}</button>
                    {closeBtn(() => { setSelField(null); setSheet("peek"); }, "Close this field")}
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 4 }}>
                    <FmOutline geom={selField.geom} size={48} color={fieldCropColor(fp.crop, fp.type_code)} dashed={!!fp.ppFull} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 700, lineHeight: 1.15, color: T.ink, overflowWrap: "anywhere" }}>{title}</div>
                      <div style={{ fontSize: 13, color: "#4f6a4c", marginTop: 2 }}>{sub}</div>
                      {who && <div style={{ fontSize: 12.5, color: "#4f6a4c", marginTop: 1 }}>{who}</div>}
                    </div>
                  </div>
                  {(fp.multi || fp.uninsurable || fp.ppFull || fp.ppPartial) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 10 }}>
                      {fp.multi && cropsOf.map((c, k) => (
                        <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: fieldCropColor(c.crop, c.type_code), flexShrink: 0 }} />
                          <b>{fmCropName(c.crop, c.type_code)}</b>
                          <span style={{ color: "#4f6a4c" }}>{c.acres != null ? fx1(c.acres) + " ac" : ""}{fmMd(c.planted) ? " · planted " + fmMd(c.planted) : ""}</span>
                        </div>
                      ))}
                      {fp.uninsurable ? <div style={{ fontSize: 13, fontWeight: 700, color: "#5f6b78" }}>Uninsurable · {fx1(fp.uiAc)} ac, no guarantee on this crop line</div>
                        : fp.ppFull ? <div style={{ fontSize: 13, fontWeight: 700, color: "#9a6a00" }}>Prevent plant · whole field · {fx1(fp.ppAc)} ac</div>
                        : fp.ppPartial ? <div style={{ fontSize: 13, fontWeight: 700, color: "#9a5a1a" }}>Partial prevent plant · {fx1(fp.plantedAc)} planted · {fx1(fp.ppAc)} PP</div>
                        : null}
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 6, marginTop: 12 }}>
                    {[["Farm", fp.fsn], ["Tract", fp.tract], ["Field", fp.field]].map(([k, v]) => (
                      <div key={k} style={{ background: "#f4f8f3", borderRadius: 10, padding: "7px 10px", minWidth: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: "#5f6f5e" }}>{k}</div>
                        <div style={{ ...uiNum, fontSize: 15, fontWeight: 800, color: T.ink, overflow: "hidden", textOverflow: "ellipsis" }}>{v || "—"}</div>
                      </div>
                    ))}
                  </div>
                  <FmPhoneFieldExtras key={cluLower(fp.clu_id) || title} cluId={fp.clu_id} farmerId={fp.farmer_id} yRow={yRow}
                    curYear={phone.year || null} curCrop={fp.crop || null} curTc={fp.type_code || null} prf={isPRFf} />
                  {fp.jdName && fp.jdName !== title && (
                    <div style={{ marginTop: 10, fontSize: 12.5, color: "#1f5480", fontWeight: 700 }}>
                      {fp.jdName}<span style={{ color: "#4f6a4c", fontWeight: 500 }}> · John Deere name</span>
                    </div>
                  )}
                  {acts.length > 0 && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 8, marginTop: 12 }}>
                      {acts.map((a, i) => <button key={a.k} onClick={a.onClick} style={bigBtn(i === 0)}>{a.label}</button>)}
                    </div>
                  )}
                  {canNameF && renaming && (
                    <form onSubmit={(ev) => {
                        ev.preventDefault();
                        const v = ev.currentTarget.elements.fname.value;
                        onSaveLabel(fp.clu_id, fp.farmer_id, v);
                        setRenaming(false);
                        setSelField((s) => (s ? { ...s, p: { ...s.p, label: String(v || "").trim() || null } } : s));
                      }}
                      style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <input name="fname" key={fp.clu_id} defaultValue={fp.label || ""} placeholder="Name this field" maxLength={60} autoFocus
                        style={{ flex: 1, minWidth: 0, height: 44, padding: "0 12px", border: "1px solid #cdd9cd", borderRadius: 10, fontSize: 16, fontFamily: "inherit", boxSizing: "border-box" }} />
                      <button type="submit" style={{ ...bigBtn(true), height: 44, padding: "0 16px" }}>Save</button>
                    </form>
                  )}
                </div>
              );
            })();

            return (
              <>
                <style>{`.fm-phone .leaflet-control-zoom{display:none}`}</style>
                {/* While chopping, the search, year and farm step aside: the list is for
                    one farm and one year, and changing either mid-pick would empty it. */}
                {!phoneChop && (
                  <div ref={setTopNode} style={{ position: "absolute", top: 10, left: 10, right: 10, zIndex: 1160, pointerEvents: "none" }}>
                    {phone.top}
                  </div>
                )}
                <div style={{ position: "absolute", right: 10, top: topH + 20, zIndex: 1060, display: "flex", flexDirection: "column", gap: 10 }}>
                  {!phoneChop && <button aria-label="Map layers" onClick={() => setLayersOpen((o) => !o)} style={fab(layersOpen ? "#a8d878" : "#fff")}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a2e1a" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" /></svg>
                    {layerCount > 0 && <span style={{ ...uiNum, position: "absolute", top: -4, right: -4, minWidth: 18, height: 18, borderRadius: 9, background: T.ink, color: "#a8d878", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", boxSizing: "border-box" }}>{layerCount}</span>}
                  </button>}
                  <button aria-label="Show where I am" onClick={locateMe} disabled={locating} style={fab("#1f5fb0")}>
                    {locating
                      ? <span style={{ width: 18, height: 18, border: "2.5px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                      : <svg width="22" height="22" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true"><path d="M21 3L3 10.5l7.5 3 3 7.5z" /></svg>}
                    <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
                  </button>
                </div>
                {meToast && (
                  <div role="status" style={{ position: "absolute", left: 10, right: 70, top: topH + 20, zIndex: 1165, background: "#fff", borderRadius: 16,
                                              padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 10px 26px rgba(0,0,0,.32)" }}>
                    <div style={{ width: 34, height: 34, borderRadius: 17, background: meToast.kind === "err" ? "#fdf0e3" : "#e7effb", flexShrink: 0,
                                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={meToast.kind === "err" ? "#a4700f" : "#1f5fb0"} strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /></svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {meToast.kind === "in" ? (() => {
                        const p = meToast.feat.properties || {};
                        return (<>
                          <div style={{ fontSize: 12, color: "#4f6a4c" }}>You're standing in</div>
                          <div style={{ fontSize: 14.5, fontWeight: 800, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {[fieldName(p), fmCropName(p.crop, p.type_code), p.acres != null ? fx1(p.acres) + " ac" : null].filter(Boolean).join(" · ")}
                          </div>
                        </>);
                      })() : <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, lineHeight: 1.35 }}>{meToast.text}</div>}
                    </div>
                    {meToast.kind === "in" && (
                      <button onClick={() => { const f = meToast.feat; setMeToast(null); openFeat(f); }}
                        style={{ height: 40, padding: "0 14px", borderRadius: 10, border: "none", background: T.ink, color: "#a8d878", fontFamily: "inherit", fontSize: 13, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>Open</button>
                    )}
                    {meToast.kind !== "in" && closeBtn(() => setMeToast(null), "Dismiss")}
                  </div>
                )}
                {/* THE CHOP PLANNER TAKES THE SHEET'S PLACE: same bottom edge, same
                    measured height (so the map frames above it), its own scroll. */}
                {phoneChop && (
                  <div ref={setSheetNode} data-fm-scroll="1"
                    style={{ position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 1150, background: "#fff", borderRadius: "22px 22px 0 0",
                             boxShadow: "0 -10px 30px rgba(0,0,0,.35)", boxSizing: "border-box", maxHeight: "62%", overflowY: "auto",
                             WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", padding: "14px 16px 16px", fontFamily: "inherit", color: T.ink }}>
                    {phone.chopPanel}
                  </div>
                )}
                {!layersOpen && !phoneChop && (
                  <div ref={setSheetNode} onTouchStart={onSheetTouchStart} onTouchEnd={onSheetTouchEnd}
                    style={{ position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 1150, background: "#fff", borderRadius: "22px 22px 0 0",
                             boxShadow: "0 -10px 30px rgba(0,0,0,.35)", display: "flex", flexDirection: "column", boxSizing: "border-box",
                             ...(sheetEff === "list" ? { height: "66%" } : sheetEff === "field" ? { maxHeight: "74%" } : {}) }}>
                    <div onClick={onHandleTap} role="button" aria-label={sheetEff === "min" ? "Show more" : "Show less"}
                      style={{ padding: "9px 0 8px", flexShrink: 0, cursor: "pointer" }}>
                      <div style={{ width: 40, height: 5, borderRadius: 3, background: "#cdd9cd", margin: "0 auto" }} />
                    </div>
                    {sheetEff === "list" ? listBody : sheetEff === "field" ? fieldBody : peekBody}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* ── INSPECTOR (third pane) ──────────────────────────────────────────
          Scope follows the selection: a clicked field wins, else selected farms,
          else the whole book. On mobile it stacks under the map rather than
          competing with it for width. */}
    </div>
  );
}

const FIELD_STATE_NM = { "36":"New York", "42":"Pennsylvania", "26":"Michigan", "39":"Ohio", "18":"Indiana", "24":"Maryland", "09":"Connecticut", "34":"New Jersey", "10":"Delaware", "51":"Virginia", "54":"West Virginia" };
const FIELD_STATE_AB = { "36":"NY", "42":"PA", "26":"MI", "39":"OH", "18":"IN", "24":"MD", "09":"CT", "34":"NJ", "10":"DE", "51":"VA", "54":"WV" };

// ⚠ THE STATE MUST COME FROM THE SAME AUTHORITY AS THE COUNTY BESIDE IT, and the
// obvious source is the wrong one. `policy_fields.state_fips` is the FSA
// ADMINISTRATIVE state, while the county these surfaces print is the coverage's
// (`insured_county`) — and those disagree on 31 CY2026 rows, every one of them a
// field whose FSA farm is administered across a state line:
//
//   IN-942-6101305-26  insured STEUBEN  ·  state_fips 39 = OHIO   (Steuben is INDIANA)
//   MP-0936367         insured TIOGA    ·  state_fips 42 = PA     (that coverage is NY)
//
// Printing state_fips beside the printed county yields "STEUBEN Co., OH" — a county
// that does not exist in Ohio — on a document a grower carries to FSA. TIOGA is the
// sharper case, because Tioga County exists in BOTH New York and Pennsylvania, so the
// wrong label is not even self-evidently wrong.
//
// So each rung of the county ladder takes its own state, and the rung that has no
// state authority returns NULL rather than borrowing one. Measured 2026-09-12:
// `insured_county` is present on ALL 16,002 CY2026 rows and its policy always
// resolves a non-null state, so rung 1 answers every field in the book today.
const fieldStateOf = (r) => {
  if (!r) return null;
  if (r.insured_county) return (r.policies && r.policies.state) || null;
  if (r._physCounty) return null;   // the polygon classifier does not report its state
  return FIELD_STATE_AB[String(r.state_fips || "")] || null;
};

// Reusable checkbox multi-select dropdown for the Field Mapper filter bar.
// `selected` is a Set; `onChange` gets the next Set. options = string[] | {value,label}[].
function FieldMultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const opts = options.map(o => typeof o === "string" ? { value:o, label:o } : o);
  const toggle = (v) => { const n = new Set(selected); n.has(v) ? n.delete(v) : n.add(v); onChange(n); };
  const summary = selected.size === 0 ? `All ${label}` : (selected.size === 1
    ? (opts.find(o => selected.has(o.value))?.label || `1 ${label}`)
    : `${selected.size} ${label}`);
  const btn = { padding:"7px 10px", borderRadius:8, border:"1px solid #cdd9cd", background: selected.size ? "#eef3ee" : "#fff", fontSize:13, color:"#1a2e1a", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontWeight: selected.size ? 700 : 400 };
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={()=>setOpen(o=>!o)} style={btn}>{summary}<span style={{ fontSize:10, color:"#60735f" }}>▼</span></button>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:30, background:"#fff", border:"1px solid #cdd9cd", borderRadius:8, boxShadow:"0 6px 22px rgba(0,0,0,0.14)", maxHeight:280, overflowY:"auto", minWidth:190, padding:4 }}>
          {selected.size > 0 && (
            <div onClick={()=>onChange(new Set())} style={{ padding:"5px 8px", fontSize:12, fontWeight:700, color:"#4a6e4a", cursor:"pointer", borderBottom:"1px solid #eef2ee", marginBottom:2 }}>✕ Clear {label}</div>
          )}
          {opts.map(o => {
            const on = selected.has(o.value);
            return (
              <label key={o.value} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 8px", cursor:"pointer", fontSize:13, borderRadius:5, background: on ? "#eef3ee" : "transparent" }}>
                <input type="checkbox" checked={on} onChange={()=>toggle(o.value)} style={{ cursor:"pointer" }} />
                <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.label}</span>
              </label>
            );
          })}
          {opts.length === 0 && <div style={{ padding:"6px 8px", fontSize:12, color:"#677167" }}>none</div>}
        </div>
      )}
    </div>
  );
}

// ── FIELD WEATHER ─────────────────────────────────────────────────────────
// Per-field historical weather, off the nearest high-res gridMET/MRMS cell
// (wny_grid_weather_daily). Resolution is ~4 km — true field-level, not county.
// All display is °F / inches (model stores °C / mm; convert at the display layer).
const cToF = (c) => c == null ? null : c * 9 / 5 + 32;
const mmToIn = (mm) => mm == null ? null : mm / 25.4;
const haversineMi = (a, b) => {
  const R = 3958.8, d2r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * d2r, dLon = (b[1] - a[1]) * d2r;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * d2r) * Math.cos(b[0] * d2r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};

// Lightweight inline-SVG chart — daily bars (optional) + one or two lines, shared
// x by day index. Keeps the page dependency-free (no D3/Recharts needed here).
//
// Callers hand in one entry per CALENDAR DAY (wxField.fieldSeries pads interior gaps
// with nulls), so x by index is x by date. `missing` marks days with no reading: they
// get a small grey tick on the baseline, so a gap never looks like a measured 0.
// The SVG is drawn at the width it actually has (measured), so text keeps its shape
// on a phone instead of being stretched; the tooltip follows a finger as well as a
// mouse. `zero={false}` lets a temperature chart start near its own lowest value.
function WxChart({ days, bars, lines, bands, refLines = [], yLabel, y2Label, height = 180, color = "#2d6a1a", fmtY = (v)=>v, fmtY2 = null, tooltip = null, missing = null, zero = true }) {
  const [hov, setHov] = useState(null);   // hovered day index (for the tooltip/guide)
  const boxRef = useRef(null);
  const [boxW, setBoxW] = useState(760);
  useEffect(() => {
    const el = boxRef.current; if (!el) return undefined;
    const read = () => { const w = Math.round(el.getBoundingClientRect().width); if (w > 0) setBoxW(w); };
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read); ro.observe(el);
    return () => ro.disconnect();
  }, [days.length === 0]); // eslint-disable-line
  const W = Math.max(280, boxW), H = height, padL = 46, padR = (lines && lines.length > 1) || y2Label ? 50 : 14, padT = 26, padB = 22;
  const n = days.length;
  if (!n) return <div style={{ fontSize:12, color:"#677167", padding:"14px 4px" }}>No data in range.</div>;
  const iw = W - padL - padR, ih = H - padT - padB;
  const xx = (i) => padL + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
  const bw = Math.max(1, iw / n * 0.7);
  // primary y-domain (bars + bands + line[0])
  let yMin = zero ? 0 : Infinity, yMax = zero ? 0 : -Infinity;
  const pushV = (v) => { if (v == null || !Number.isFinite(v)) return; if (v > yMax) yMax = v; if (v < yMin) yMin = v; };
  (bars || []).forEach(pushV);
  (bands || []).forEach(b => { pushV(b[0]); pushV(b[1]); });
  (lines || []).filter(l => !l.axis2).forEach(l => l.data.forEach(pushV));
  refLines.filter(r => !r.axis2).forEach(r => pushV(r.v));
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) { yMin = 0; yMax = 1; }
  if (yMax === yMin) yMax = yMin + 1;
  const pad = (yMax - yMin) * 0.08; yMax += pad; if (yMin < 0 || !zero) yMin -= pad;
  const yy = (v) => padT + ih - ((v - yMin) / (yMax - yMin)) * ih;
  // secondary axis (cumulative line)
  const a2 = (lines || []).filter(l => l.axis2);
  let y2Max = 0; a2.forEach(l => l.data.forEach(v => { if (v != null && v > y2Max) y2Max = v; }));
  if (y2Max === 0) y2Max = 1;
  const yy2 = (v) => padT + ih - (v / y2Max) * ih;
  const ticks = 4;
  const gridY = Array.from({ length: ticks + 1 }, (_, k) => yMin + (k / ticks) * (yMax - yMin));
  const linePath = (data, scale) => data.map((v, i) => v == null ? null : `${i === 0 || data[i-1] == null ? "M" : "L"}${xx(i).toFixed(1)},${scale(v).toFixed(1)}`).filter(Boolean).join(" ");
  // sparse x labels, about one per 110px; the year rides on the first label and on
  // any label where it changes, so a range across Jan 1 can't be misread
  const step = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 110))));
  const xLab = (i) => { const d = String(days[i]); const prev = i - step >= 0 ? String(days[i - step]) : null;
    return WXF.shortDate(d, i === 0 || (prev && prev.slice(0, 4) !== d.slice(0, 4))); };
  const hovX = hov != null ? xx(hov) : null;
  const fmtDate = (d) => d ? WXF.shortDate(String(d), true) : "";
  // tooltip horizontal placement (% of width) + edge-aware anchor so it never clips
  const hovFrac = hovX != null ? hovX / W : 0;
  const ttTransform = hovFrac < 0.16 ? "translateX(4px)" : hovFrac > 0.84 ? "translateX(-100%) translateX(-4px)" : "translateX(-50%)";
  const pick = (e) => { const r = e.currentTarget.getBoundingClientRect(); const f = (e.clientX - r.left) / r.width; setHov(Math.max(0, Math.min(n - 1, Math.round(f * (n - 1))))); };
  return (
    <div ref={boxRef} style={{ position:"relative", width:"100%" }}>
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display:"block" }}>
      {gridY.map((g, k) => {
        const frac = (g - yMin) / (yMax - yMin);                 // height fraction → secondary-axis value
        const f2 = fmtY2 || ((v) => (Math.round(v * 10) / 10).toString());
        return (
          <g key={k}>
            <line x1={padL} x2={W - padR} y1={yy(g)} y2={yy(g)} stroke="#eef2ee" strokeWidth="1" />
            <text x={padL - 5} y={yy(g) + 3} textAnchor="end" fontSize="10" fill="#677167">{fmtY(g)}</text>
            {a2.length > 0 && <text x={W - padR + 5} y={yy(g) + 3} textAnchor="start" fontSize="10" fill={a2[0].color || "#677167"}>{f2(frac * y2Max)}</text>}
          </g>
        );
      })}
      {yLabel && <text x={padL - 5} y={10} textAnchor="end" fontSize="10" fontWeight="700" fill="#60735f">{yLabel}</text>}
      {y2Label && <text x={W - padR + 5} y={10} textAnchor="start" fontSize="10" fontWeight="700" fill={a2[0]?.color || "#60735f"}>{y2Label}</text>}
      {(bands ? days : []).map((d, i) => bands[i] && bands[i][0] != null && bands[i][1] != null ? (
        <rect key={"b"+i} x={xx(i) - bw/2} y={yy(bands[i][1])} width={bw} height={Math.max(0, yy(bands[i][0]) - yy(bands[i][1]))} fill={color} opacity="0.20" />
      ) : null)}
      {(bars ? days : []).map((d, i) => bars[i] != null && bars[i] > 0 ? (
        <rect key={"r"+i} x={xx(i) - bw/2} y={yy(bars[i])} width={bw} height={Math.max(0, yy(0) - yy(bars[i]))} fill={color} opacity={i === hov ? 1 : 0.72} />
      ) : null)}
      {(missing ? days : []).map((d, i) => missing[i] ? (
        <rect key={"m"+i} x={xx(i) - Math.max(1, bw)/2} y={padT + ih - 3} width={Math.max(1, bw)} height={3} fill="#b9c2b9" />
      ) : null)}
      {refLines.map((r, k) => (
        <g key={"ref"+k}>
          <line x1={padL} x2={W - padR} y1={yy(r.v)} y2={yy(r.v)} stroke={r.color || "#d08a2a"} strokeWidth="1" strokeDasharray="4,3" opacity="0.8" />
          {r.label && <text x={W - padR - 2} y={yy(r.v) - 2} textAnchor="end" fontSize="9.5" fill={r.color || "#d08a2a"}>{r.label}</text>}
        </g>
      ))}
      {(lines || []).map((l, k) => (
        <path key={"l"+k} d={linePath(l.data, l.axis2 ? yy2 : yy)} fill="none" stroke={l.color || color} strokeWidth={l.width || 2} opacity={l.opacity ?? 1} strokeDasharray={l.dash || undefined} />
      ))}
      {days.map((d, i) => i % step === 0 ? (
        <text key={"x"+i} x={xx(i)} y={H - 6} textAnchor={i === 0 ? "start" : "middle"} fontSize="10" fill="#677167">{xLab(i)}</text>
      ) : null)}
      {/* hover guide + markers */}
      {hov != null && (
        <g pointerEvents="none">
          <line x1={hovX} x2={hovX} y1={padT} y2={padT + ih} stroke="#1a2e1a" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
          {(bands && bands[hov]) ? [bands[hov][0], bands[hov][1]].map((v, k) => v != null ? (
            <circle key={"hb"+k} cx={hovX} cy={yy(v)} r="3" fill="#fff" stroke={color} strokeWidth="1.5" />
          ) : null) : null}
          {(lines || []).map((l, k) => l.data[hov] != null ? (
            <circle key={"hl"+k} cx={hovX} cy={(l.axis2 ? yy2 : yy)(l.data[hov])} r="3.2" fill={l.color || color} stroke="#fff" strokeWidth="1.3" />
          ) : null)}
        </g>
      )}
      {/* capture layer (on top): mouse, pen and finger all map x → nearest day. pan-y
          keeps a vertical swipe scrolling the page. */}
      <rect x={padL} y={padT} width={iw} height={ih} fill="transparent" style={{ touchAction:"pan-y" }}
        onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHov(null)} />
    </svg>
    {hov != null && tooltip && (
      <div style={{ position:"absolute", top:2, left:`${hovFrac * 100}%`, transform: ttTransform, pointerEvents:"none",
        background:"#1a2e1a", color:"#fff", borderRadius:7, padding:"6px 9px", fontSize:11.5, whiteSpace:"nowrap", boxShadow:"0 3px 12px rgba(0,0,0,0.28)", zIndex:5 }}>
        <div style={{ fontWeight:800, marginBottom:3, color:"#cfe8b0" }}>{fmtDate(days[hov])}</div>
        {tooltip(hov).map((t, k) => (
          <div key={k} style={{ display:"flex", alignItems:"center", gap:6, lineHeight:1.5 }}>
            <span style={{ width:8, height:8, borderRadius:2, background: t.color || "#a8d878", display:"inline-block" }} />
            <span style={{ color:"#bcd9bc" }}>{t.label}</span>
            <span style={{ marginLeft:"auto", fontWeight:700, fontVariantNumeric:"tabular-nums" }}>{t.value}</span>
          </div>
        ))}
      </div>
    )}
    </div>
  );
}

// ── PRF RAINFALL for one field ──────────────────────────────────────────────
// The farmer's question, answered per field: how much OFFICIAL (NOAA/RMA) rain
// has this grid gotten, what's normal, and am I going to trigger? Resolves the
// field's PRF grid from its centroid (point-in-bbox on prf_grid_locations), then
// shows the grower's CHOSEN intervals (PRF requires ≥2, ≤50% each) as rainfall
// progress bars with the trigger line marked, plus the full-grid interval strip.
const _PRF_ORDER = ["JANFEB","FEBMAR","MARAPR","APRMAY","MAYJUN","JUNJUL","JULAUG","AUGSEP","SEPOCT","OCTNOV","NOVDEC","DECJAN"];
const _PRF_LABEL = { ...PRF_INTERVAL_LABELS, DECJAN:"Dec–Jan" };
const _PRF_MR = { JANFEB:[0,1],FEBMAR:[1,2],MARAPR:[2,3],APRMAY:[3,4],MAYJUN:[4,5],JUNJUL:[5,6],JULAUG:[6,7],AUGSEP:[7,8],SEPOCT:[8,9],OCTNOV:[9,10],NOVDEC:[10,11],DECJAN:[11,0] };
// `year` is the crop year being read; omitted, it is this calendar year (as before).
// A prior crop year's intervals have all closed, so they read 1 rather than being
// timed against this year's calendar.
// ⚠ TIMED BY THE SHARED MODULE (src/weather/wxPrf.js), NOT _PRF_MR. The Rainfall
// Index has eleven intervals, JANFEB..NOVDEC, and no DECJAN; the local table above
// still carries DECJAN for the Field Mapper's interval picker, and timed it from the
// prior December. A code WXP does not know is null here, never timed.
function _prfElapsed(code, year = null) {
  return WXP.elapsed(code, year || new Date().getUTCFullYear(), Date.now());
}
// Days remaining until an interval closes (negative once closed). Drives the
// "don't show estimated indemnity until within 15 days of interval end" gate.
function _prfDaysToEnd(code, year = null) {
  const b = WXP.intervalBounds(code, year || new Date().getUTCFullYear());
  if (!b) return null;
  return (b.end - Date.now()) / 86400000;
}
// The view's interval order: the eleven RMA intervals in calendar order, then any
// code the module does not know (kept, so a line is never silently dropped; it
// reads as an unknown interval rather than being timed).
function _prfViewCodes(codes) {
  const set = new Set([...(codes || [])].filter(Boolean));
  return [...WXP.ORDER.filter((c) => set.has(c)), ...[...set].filter((c) => !WXP.ORDER.includes(c)).sort()];
}
// One grid-interval as this view draws it, read through the Weather page's own rule
// (WXP.readInterval) so the two pages can never disagree about one interval.
// `row` is the prf_grid_indices row, or null when none is stored.
//   state  final | estimate | sofar | upcoming | nodata | unknown
//   index  final or estimate-at-close only; null while open (never judged)
//   toDate open (or closed short of its last day): percent of normal to date
function _prfFieldReading(code, row, year) {
  const rd = WXP.readInterval({ code, entry: row || null, coverageLevel: null, year, nowMs: Date.now(), finalInches: prfFinalInches });
  return { code, state: rd.state, normal: rd.normal, normalSoFar: rd.normalToDate, actual: rd.inches,
           index: rd.index, toDate: rd.toDate, isFinal: rd.state === "final",
           asOf: (row && row.estimated_as_of) || null };
}

// The 2-month PRF interval that starts in the current month (map default), on the
// viewer's own calendar. RMA has no Dec–Jan interval, so December shows Nov–Dec.
function currentPrfInterval() {
  const m = new Date().getMonth(); // 0-11, local
  return WXP.ORDER[Math.min(m, WXP.ORDER.length - 1)] || "MAYJUN";
}
// Color a grid cell by its rainfall index (percent of normal; 100 = normal).
function prfIndexColor(idx) {
  if (idx == null) return "#b8c2b8";
  if (idx < 70) return "#b5321f";
  if (idx < 85) return "#d9663a";
  if (idx < 100) return "#e8b53a";
  if (idx < 115) return "#a8d878";
  return "#4a9a4a";
}

// The words for one reading's state, the Weather page's (WXP_STATE) where the state is
// the same, so the two pages name an interval alike. A closed interval whose rain total
// stops short of its last day reads to date, and says it is closed.
function _prfStateLabel(d, year) {
  const st = d && d.state;
  if (st === "final") return "Final";
  if (st === "estimate") return "Estimate at close";
  if (st === "sofar") return (_prfElapsed(d.code, year) ?? 0) >= 1 ? "Closed, rain to date" : "Interval open";
  if (st === "upcoming") return "Not started";
  if (st === "unknown") return "Unknown interval";
  return "No data yet";
}
function _prfStateColor(st) {
  return st === "final" ? "#2d6a2d" : st === "estimate" ? "#7a5a20" : st === "sofar" ? "#5b6f8a" : "#677167";
}

function FieldPRFRainfallView({ field, isMobile = false, onBack, mode = null, hideBack = false, cropYear = null }) {
  const isPortal = mode === "portal";   // farmers see ONLY their insured intervals, not the whole grid
  const [center, setCenter] = useState(undefined);   // undefined=loading, null=none, [lat,lon]
  const [grid, setGrid] = useState(undefined);        // undefined=loading, null=not found, number
  const [gridApprox, setGridApprox] = useState(false);// grid came from the policy (no bbox), not the exact centroid
  const [idx, setIdx] = useState(null);               // code -> {normal, actual, index, isFinal, asOf}
  const [coverage, setCoverage] = useState(null);     // decimal (0.90)
  const [policyIvs, setPolicyIvs] = useState(null);   // [{code, grid, liability, acres, pct}] across the policy
  const [fpi, setFpi] = useState(null);               // THIS field's PRF lines from field_prf_intervals (M13-authoritative)
  // THE CROP YEAR OF THE FIELDS ON SCREEN. This read CURRENT_CROP_YEAR, so opening a
  // field on the 2025 map gave 2026 intervals here while the field sheet's PRF tile
  // (FmPhoneFieldExtras, curYear) showed 2025's — one field, two years, a tap apart.
  const year = cropYear || CURRENT_CROP_YEAR;

  // Authoritative per-field PRF coverage from the M13 (field_prf_intervals): the
  // field's own grid, intended use (haying/grazing), and each interval it's insured
  // in with its % and protection/acre. No centroid guess, no block ambiguity.
  useEffect(() => {
    let alive = true;
    // Match by the field's unique CLU GUID OR by its policy+Farm-Tract-Field. Either
    // alone can miss (some SOI-loaded rows have no CLU; a split field's PRF may sit
    // under a different policy_number than its crop), so we OR the two keys.
    const fld = field.field_id != null ? field.field_id : field.field;
    const ors = [];
    if (field.clu_id) ors.push(`clu_id.ilike.${field.clu_id}`);   // stored CLUs are mixed case
    if (field.policy_number && field.fsn && field.tract && fld != null)
      ors.push(`and(policy_number.eq.${field.policy_number},fsn.eq.${field.fsn},tract.eq.${field.tract},field_id.eq.${fld})`);
    if (!ors.length) { setFpi([]); return; }
    // ⚠ ONE CROP YEAR. Unfiltered, a field holding lines for two crop years had them
    // overwrite each other per interval below (and its grid and use came from
    // whichever row the database returned first), so the 2025 page could show 2026's
    // percents. Only the year on screen is read.
    setFpi(null);
    sbFetch("field_prf_intervals", { select: "grid_id,interval_code,intended_use,percent_value,protection_per_acre,coverage_level,county,crop_year,policy_id,policy_number",
      filters: [`or=(${ors.join(",")})`, `crop_year=eq.${year}`] })
      .then(rows => { if (alive) setFpi(rows || []); })
      .catch(() => { if (alive) setFpi([]); });
    return () => { alive = false; };
  }, [field, year]);

  // Reduce the field's rows to one use + grid + per-interval coverage.
  const fieldCov = useMemo(() => {
    if (!fpi || !fpi.length) return null;
    const use = fpi[0].intended_use;
    const gid = fpi[0].grid_id;
    const cl = fpi.find(r => r.coverage_level != null)?.coverage_level ?? null;
    const ivs = {};
    for (const r of fpi) ivs[r.interval_code] = { pct: Number(r.percent_value) || 0, protPerAc: Number(r.protection_per_acre) || 0 };
    const codes = _prfViewCodes(Object.keys(ivs));
    const totalCovPerAc = codes.reduce((s, c) => s + ivs[c].protPerAc * ivs[c].pct, 0);
    return { use, grid: gid, coverageLevel: cl, ivs, codes, totalCovPerAc };
  }, [fpi]);
  // The M13 field→grid is authoritative — use it (and its coverage level) over the centroid guess.
  useEffect(() => {
    if (fieldCov?.grid != null) { setGrid(fieldCov.grid); setGridApprox(false); }
    if (fieldCov?.coverageLevel != null) setCoverage(fieldCov.coverageLevel);
  }, [fieldCov]);
  const policyGrids = useMemo(() => policyIvs ? [...new Set(policyIvs.map(iv => String(iv.grid)).filter(Boolean))] : [], [policyIvs]);
  const chosen = useMemo(() => {                        // this grid's chosen intervals (union across blocks)
    if (!policyIvs || grid == null) return {};
    const m = {};
    for (const iv of policyIvs) { if (String(iv.grid) !== String(grid)) continue; const e = m[iv.code] || { liability: 0, acres: 0, pct: 0 }; e.liability += iv.liability; e.acres = iv.acres || e.acres; e.pct = iv.pct || e.pct; m[iv.code] = e; }
    return m;
  }, [policyIvs, grid]);
  // PRF coverage on one grid can be split into multiple acreage "blocks", each a
  // valid ~100% interval allocation. Group by insured acres so we show each block's
  // real split instead of merging them (which produced summed >100% percentages).
  // R&H addresses PRF per CLU; until we ingest that field→block link from M13, we
  // show every block on the field's grid rather than guessing which one the field is in.
  const blocks = useMemo(() => {
    if (!policyIvs || grid == null) return [];
    const by = {};
    for (const iv of policyIvs) {
      if (String(iv.grid) !== String(grid)) continue;
      const key = String(iv.acres || 0);
      if (!by[key]) by[key] = { acres: iv.acres || 0, intervals: {}, liability: 0 };
      const b = by[key];
      const e = b.intervals[iv.code] || { pct: 0, liability: 0 };
      e.pct = iv.pct || e.pct; e.liability += iv.liability; b.intervals[iv.code] = e;
      b.liability += iv.liability;
    }
    return Object.values(by)
      .map(b => ({ ...b, codes: _prfViewCodes(Object.keys(b.intervals)) }))
      .sort((a, b) => b.acres - a.acres);
  }, [policyIvs, grid]);

  // 1) resolve the field's centroid (R&H lat/lon, else the CLU boundary centroid)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (field.latitude != null && field.longitude != null) { if (alive) setCenter([Number(field.latitude), Number(field.longitude)]); return; }
      if (field.clu_id) {
        // ⚠ `eq.` IS CASE-SENSITIVE and policy_fields is 59% UPPER-case. Measured 2026-10-01:
        // 15,225 CY2026 fields have no lat/lon of their own and so depend on this lookup, and
        // 9,005 of them (59%) got nothing back — `setCenter(null)`, a field that cannot be
        // centred. The sibling lookup twenty lines below already used `ilike`, which is why one
        // of the two worked; a rule written twice drifts, so both fold now. See `cluLower`.
        try { const d = await sbFetch("field_geometries", { select: "centroid_lat,centroid_lon", filters: [`clu_id=eq.${cluLower(field.clu_id)}`] }); const g = d && d[0]; if (alive) setCenter(g && g.centroid_lat != null ? [Number(g.centroid_lat), Number(g.centroid_lon)] : null); return; } catch (e) {}
      }
      if (alive) setCenter(null);
    })();
    return () => { alive = false; };
  }, [field]);

  // 2) the grower's coverage + the intervals/grids their policy insures (grid-independent)
  // ⚠ THE SAME CROP YEAR AS THE PAGE. prf_policies carries no crop_year of its own,
  // and this read it by policy_number alone and took the first row, which could be
  // another year's record (an empty next-year renewal shell, or last year's). It is
  // now read by the parent policy id of THIS year: the field's own PRF line for the
  // year names it (field_prf_intervals.policy_id, already filtered to `year`), else
  // the field row's policy_id when that row is this year's. Neither known, or two
  // policies named: nothing, never a guess at another year's record.
  const policyKey = useMemo(() => {
    if (fpi === null) return undefined;                      // wait for the field's lines
    const pn = field.policy_number != null ? String(field.policy_number) : null;
    const ids = [...new Set((fpi || []).filter(r => r.policy_id && (!pn || String(r.policy_number) === pn)).map(r => String(r.policy_id)))];
    if (ids.length === 1) return ids[0];
    if (ids.length > 1) return null;                         // two of this year's policies: don't pick one
    // A split field's PRF can sit under another policy number than its crop row: the
    // field's own PRF lines for the year still name that policy.
    const any = [...new Set((fpi || []).map(r => r.policy_id).filter(Boolean).map(String))];
    if (any.length === 1) return any[0];
    if (!any.length && field.policy_id && Number(field.crop_year) === Number(year)) return String(field.policy_id);
    return null;
  }, [fpi, field.policy_number, field.policy_id, field.crop_year, year]);
  useEffect(() => {
    if (policyKey === undefined) { setPolicyIvs(null); return; }
    if (!policyKey) { setPolicyIvs([]); return; }
    let alive = true;
    setPolicyIvs(null);
    (async () => {
      try {
        const pol = await sbFetch("prf_policies", { select: "id,coverage_level,policy_id,county", filters: [`policy_id=eq.${policyKey}`] });
        const all = (pol || []).filter(p => p && p.id);
        // One policy can hold a prf_policies row per county; keep the field's county
        // when it names one of them, else every row of THIS year's policy.
        const fc = String(field.county || "").trim().toUpperCase();
        const sameCounty = all.filter(p => fc && String(p.county || "").trim().toUpperCase() === fc);
        const pps = sameCounty.length ? sameCounty : all;
        if (!pps.length) { if (alive) setPolicyIvs([]); return; }
        const cl = pps.find(p => p.coverage_level != null);
        if (alive && cl) setCoverage(Number(cl.coverage_level));
        const ivs = await sbFetch("prf_intervals", { select: "interval_code,liability,insured_acres,percent_value,grid_id", filters: [`prf_policy_id=in.(${pps.map(p => p.id).join(",")})`] });
        if (alive) setPolicyIvs((ivs || []).map(iv => ({ code: parsePRFCode(iv.interval_code), grid: iv.grid_id, liability: Number(iv.liability) || 0, acres: Number(iv.insured_acres) || 0, pct: Number(iv.percent_value) || 0 })).filter(iv => iv.code));
      } catch (e) { if (alive) setPolicyIvs([]); }
    })();
    return () => { alive = false; };
  }, [policyKey, field.county]);

  // 3) resolve the field's grid: exact bbox hit on the centroid; else fall back to the
  // policy's own grid (RMA grids the policy insures) so it still works where we don't
  // have a grid-location bbox loaded (e.g. Michigan) — flagged approximate for multi-grid.
  useEffect(() => {
    if (center === undefined || policyIvs === null) return;
    let alive = true;
    sbFetch("prf_grid_locations", { select: "grid_id,min_lat,max_lat,min_lon,max_lon" })
      .then(rows => {
        if (!alive) return;
        let hit = null;
        if (center) { const [lat, lon] = center; const g = (rows || []).find(g => lat >= Number(g.min_lat) && lat <= Number(g.max_lat) && lon >= Number(g.min_lon) && lon <= Number(g.max_lon)); if (g) hit = Number(g.grid_id); }
        if (hit != null) { setGrid(hit); setGridApprox(false); return; }
        if (policyGrids.length) { setGrid(Number(policyGrids[0])); setGridApprox(policyGrids.length > 1); return; }  // no bbox → use the policy's grid
        setGrid(null);
      })
      .catch(() => { if (alive) { if (policyGrids.length) { setGrid(Number(policyGrids[0])); setGridApprox(policyGrids.length > 1); } else setGrid(null); } });
    return () => { alive = false; };
  }, [center, policyIvs, policyGrids]);

  // 4) grid → the year's rainfall/index per interval
  useEffect(() => {
    if (grid == null) return;
    let alive = true;
    sbFetch("prf_grid_indices", { select: "interval_code,normal_rainfall_inches,normal_to_date_inches,estimated_rainfall_inches,final_rainfall_inches,estimated_index_value,final_index_value,is_final,estimated_as_of", filters: [`grid_id=eq.${grid}`, `crop_year=eq.${year}`] })
      .then(rows => { if (!alive) return; const m = {};
        // One rule for every PRF screen: read through the Weather page's module
        // (WXP.readInterval, itself on src/prf/prfIntervalStatus.js). An open interval
        // has no index, only percent of normal to date against the normal for the
        // same days, and is never judged against the trigger.
        for (const r of (rows || [])) m[r.interval_code] = _prfFieldReading(r.interval_code, r, year);
        setIdx(m); })
      .catch(() => { if (alive) setIdx({}); });
    return () => { alive = false; };
  }, [grid, year]);

  // An insured interval with no stored row still has a state: not started, or no data yet.
  const readOf = (c) => (idx && idx[c]) || _prfFieldReading(c, null, year);
  const card = { background: "#fff", border: "1px solid #d9e4d9", borderRadius: 12, padding: isMobile ? 14 : 18 };
  const back = { padding: "7px 12px", borderRadius: 8, border: "1px solid #cdd9cd", background: "#fff", fontSize: 13, color: "#2d4a2d", cursor: "pointer", fontWeight: 700 };
  const covPct = coverage != null ? coverage * 100 : null;
  const loading = center === undefined || grid === undefined || (grid != null && idx === null);

  // A single rainfall progress bar: rain fills toward normal (100%); the trigger
  // line sits below 100% at the coverage level. Scale caps at 160% of normal.
  const RainBar = ({ code, big, cov }) => {
    const d = idx && idx[code]; if (!d || (d.index == null && d.toDate == null)) return null;
    const elapsed = _prfElapsed(code, year);
    const open = d.index == null;            // read to date: no index, no trigger call, no dollars
    const shown = open ? d.toDate : d.index;
    const scale = 160;
    const pos = Math.min(shown, scale) / scale * 100;
    const trigPos = covPct != null ? Math.min(covPct, scale) / scale * 100 : null;
    const normPos = 100 / scale * 100;
    const below = !open && covPct != null && d.index < covPct;
    const meaningful = !open;
    const fill = open ? "#7f9bb8" : below ? "#d64545" : d.index < 100 ? "#e0932a" : "#3a9a4a";
    const season = PRF_INTERVAL_SEASON[code] || "spring";
    const sc = PRF_SEASON_COLOR[season] || { bg: "#eaf0e8", color: "#2d5a2d" };
    const pay = (!open && below && covPct != null) ? Math.max(0, (coverage - d.index / 100) / coverage) : 0;
    const ch = cov !== undefined ? cov : (chosen && chosen[code]);   // per-block override when provided
    const payout = ch && pay > 0 && !open ? ch.liability * pay : null;
    return (
      <div style={{ marginBottom: big ? 14 : 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
          <span style={{ fontSize: big ? 14 : 12.5, fontWeight: 800, color: sc.color, background: sc.bg, padding: "2px 10px", borderRadius: 10 }}>{WXP.LABEL[code] || code}</span>
          <span style={{ fontSize: big ? 13.5 : 12, fontWeight: 800, color: fill }}>{shown.toFixed(0)}%<span style={{ fontWeight: 500, color: "#60735f" }}> of normal{open ? " so far" : ""}</span></span>
          {d.isFinal ? <span style={{ fontSize: 10, fontWeight: 800, color: "#2d6a2d", background: "#e3f3e0", padding: "1px 7px", borderRadius: 8 }}>{isPortal ? "Final" : "RMA FINAL"}</span>
            : d.state === "estimate" ? <span style={{ fontSize: 10, fontWeight: 700, color: "#7a5a20", background: "#fbf2e0", padding: "1px 7px", borderRadius: 8 }}>est · closed</span>
            : elapsed >= 1 ? <span style={{ fontSize: 10, fontWeight: 700, color: "#4a6e4a", background: "#eef3ee", padding: "1px 7px", borderRadius: 8 }}>closed · rain to date</span>
            : elapsed > 0 ? <span style={{ fontSize: 10, fontWeight: 700, color: "#4a6e4a", background: "#eef3ee", padding: "1px 7px", borderRadius: 8 }}>{open ? "open · " : ""}{Math.round(elapsed * 100)}% elapsed</span>
            : <span style={{ fontSize: 10, fontWeight: 700, color: "#677167", background: "#f2f4f2", padding: "1px 7px", borderRadius: 8 }}>upcoming</span>}
          {meaningful && below && <span style={{ fontSize: 10.5, fontWeight: 800, color: "#b03030" }}>below {covPct.toFixed(0)}% trigger</span>}
          {ch && <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 800, color: "#1a5a8a", background: "#e2eef7", padding: "1px 8px", borderRadius: 8 }}>◆ you insured this{ch.pct ? ` · ${Math.round(ch.pct * 100)}%` : ""}</span>}
        </div>
        {/* the bar */}
        <div style={{ position: "relative", height: big ? 26 : 20, background: "#eef2ee", borderRadius: 7, overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, left: 0, width: `${pos}%`, background: fill, opacity: 0.85, borderRadius: 7, transition: "width .3s" }} />
          {/* normal (100%) marker */}
          <div style={{ position: "absolute", top: 0, bottom: 0, left: `${normPos}%`, width: 2, background: "#2d4a2d", opacity: 0.5 }} title="Normal (100%)" />
          {/* trigger line */}
          {trigPos != null && !open && <div style={{ position: "absolute", top: 0, bottom: 0, left: `${trigPos}%`, width: 0, borderLeft: "2px dashed #b03030" }} title={`Trigger ${covPct.toFixed(0)}%`} />}
        </div>
        {/* inches */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3, fontSize: big ? 12 : 11, color: "#54724f" }}>
          <span><b style={{ color: "#2d4a2d" }}>{d.actual != null ? d.actual.toFixed(2) : "—"}″</b> fallen · {open
            ? `${d.normalSoFar != null ? d.normalSoFar.toFixed(2) : "—"}″ normal so far`
            : `${d.normal != null ? d.normal.toFixed(2) : "—"}″ normal`}</span>
          {/* No dollar estimate in front of a grower (Kevin, 2026-10-30: "we dont want any
              liability for dollar amounts"). The index and the trigger say the same thing. */}
          {!isPortal && payout != null && payout > 0 && <span style={{ color: "#b03030", fontWeight: 800 }}>~${Math.round(payout).toLocaleString()} {d.isFinal ? "final" : "est. at close"}</span>}
        </div>
      </div>
    );
  };

  const chosenCodes = chosen ? _prfViewCodes(Object.keys(chosen)) : [];
  const otherCodes = WXP.ORDER.filter(c => idx && idx[c] && (idx[c].index != null || idx[c].toDate != null) && !chosenCodes.includes(c));
  const asOf = idx ? Object.values(idx).map(d => d.asOf).filter(Boolean).sort().slice(-1)[0] : null;
  const anyFinal = idx ? Object.values(idx).some(d => d.isFinal) : false;
  // grid resolved but no prf_grid_indices row for this grid and crop year carries a value.
  // That is all the code knows: not which state the grid is in, nor why nothing is stored.
  const noRainData = grid != null && idx != null && !Object.values(idx).some(d => d.index != null || d.toDate != null || d.normal != null);
  // (No headline average — per Kevin, PRF is judged interval-by-interval, never averaged.)

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        {!hideBack && <button onClick={onBack} style={back}>← Back to Field Mapper</button>}
        <div>
          {!hideBack && <h1 style={{ fontSize: isMobile ? 18 : 23, fontWeight: 800, color: "#1a2e1a", margin: 0 }}>PRF Rainfall — {field.farmer || "field"}</h1>}
          <div style={{ fontSize: 12, color: "#60735f" }}>
            Farm {field.fsn || "—"} · Tract {field.tract || "—"} · Field {field.field_id || "—"}{grid != null ? ` · PRF grid ${grid}` : ""}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ ...card, color: "#60735f", textAlign: "center", padding: 40 }}>Finding this field’s rainfall grid…</div>
      ) : grid == null ? (
        <div style={{ ...card, color: "#54724f", lineHeight: 1.6 }}>
          <b style={{ color: "#1a2e1a" }}>Couldn’t place this field in a PRF grid.</b>{" "}
          {isPortal
            ? <>{center === null ? "This field has no location on file yet. " : ""}Your agent can tell you which grid it is insured in.</>
            : <>It needs a boundary centroid or R&H lat/lon to look up the NOAA grid. {center === null ? "This field has no location on file yet." : ""}</>}
        </div>
      ) : noRainData ? (
        <div style={{ ...card, color: "#7a5a20", background: "#fbf6ec", borderColor: "#e7d4a8", lineHeight: 1.6 }}>
          <b style={{ color: "#1a2e1a" }}>No rainfall index stored for grid {grid} for the {year} crop year yet.</b>{" "}
          {isPortal
            ? <>Your agent can tell you how this grid’s intervals are doing.</>
            : <>Silas has no Rainfall Index row with a value stored for this grid and year (prf_grid_indices), so nothing can show here until one is loaded.</>}
        </div>
      ) : (
        <>
          {gridApprox && (
            <div style={{ ...card, background: "#eef3ee", borderColor: "#cdd9cd", color: "#54724f", fontSize: 12, marginBottom: 12 }}>
              {isPortal
                ? <>Your policy covers {policyGrids.length} grids and we couldn’t tell which one this field sits in — showing grid <b>{grid}</b>. It may sit in another of your grids.</>
                : <>This policy spans {policyGrids.length} grids and we couldn’t pin this exact field to one (no grid-boundary data loaded here) — showing grid <b>{grid}</b>. It may sit in another of the policy’s grids.</>}
            </div>
          )}
          {/* Data-source line only — no averaging. Each interval stands on its own below. */}
          <div style={{ ...card, marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 12px" }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, color: anyFinal ? "#2d6a2d" : "#7a5a20" }}>{isPortal
              ? (anyFinal ? "Final and estimated rainfall" : "Estimated rainfall")
              : (anyFinal ? "RMA Final + NOAA estimate" : "NOAA estimate")}</span>
            {asOf && <span style={{ fontSize: 11.5, color: "#60735f" }}>· rainfall through {asOf}</span>}
            <span style={{ fontSize: 11.5, color: "#60735f", marginLeft: "auto" }}>Per-interval rainfall below — nothing averaged</span>
          </div>

          {/* ── FIELD-SPECIFIC PRF (authoritative, from M13 field_prf_intervals) ── */}
          {fieldCov && (() => {
            const acres = field.acres != null ? Number(field.acres) : null;
            const covLabel = fieldCov.use === "HAYING" ? { t: "Haying", bg: "#eaf3e0", c: "#3a6a1a" }
                            : fieldCov.use === "GRAZING" ? { t: "Grazing", bg: "#eef0f7", c: "#3a4a7a" }
                            : { t: "PRF", bg: "#eef2ee", c: "#4a6e4a" };
            return (
              <div style={{ ...card, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a" }}>Farm {field.fsn || "—"} · Tract {field.tract || "—"} · Field {field.field_id || "—"}</div>
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: covLabel.c, background: covLabel.bg, padding: "2px 10px", borderRadius: 10 }}>{covLabel.t}</span>
                  {acres != null && <span style={{ fontSize: 11.5, color: "#54724f" }}>{acres.toFixed(2)} ac</span>}
                  <span style={{ fontSize: 11.5, color: "#60735f" }}>Grid {fieldCov.grid}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11.5, color: "#54724f" }}>Total coverage <b style={{ color: "#1a2e1a" }}>${Math.round(fieldCov.totalCovPerAc).toLocaleString()}/ac</b>{acres != null && fieldCov.totalCovPerAc > 0 ? <span style={{ color: "#60735f" }}> · ${Math.round(fieldCov.totalCovPerAc * acres).toLocaleString()} total</span> : null}</span>
                </div>
                {/* Phone: one row per insured interval, index on the right, the rest under
                    it, in place of a seven-column table squeezed to 11px. */}
                {isMobile && (
                  <div>
                    {fieldCov.codes.map((c, ci) => {
                      const iv = fieldCov.ivs[c];
                      const d = readOf(c);
                      const covPerAc = iv.protPerAc * iv.pct;
                      const index = d && d.index != null ? d.index : null;
                      const isFinal = d && d.isFinal;
                      const cl = (fieldCov.coverageLevel != null && fieldCov.coverageLevel > 0 && fieldCov.coverageLevel <= 1) ? fieldCov.coverageLevel
                               : (coverage != null && coverage > 0 && coverage <= 1) ? coverage : null;
                      const below = index != null && cl != null && index < cl * 100;
                      const payFactor = (index != null && cl) ? Math.max(0, (cl - index / 100) / cl) : null;
                      const daysToEnd = _prfDaysToEnd(c, year);
                      const gated = isFinal || (daysToEnd != null && daysToEnd <= 15);
                      const estIndemAc = (!isPortal && gated && payFactor != null) ? covPerAc * payFactor : null;
                      return (
                        <div key={c} style={{ padding: "9px 0", borderTop: ci ? "1px solid #eef2ee" : "none" }}>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: "#1a2e1a" }}>{WXP.LABEL[c] || c}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: _prfStateColor(d.state) }}>{_prfStateLabel(d, year)}</span>
                            <span style={{ marginLeft: "auto", fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums",
                                           color: index != null ? (below ? "#b03030" : index < 100 ? "#c07a1a" : "#2d7a3a") : d.state === "sofar" ? "#5b6f8a" : "#677167" }}>
                              {index != null ? `${index.toFixed(0)}%` : d.state === "sofar" && d.toDate != null ? `${d.toDate.toFixed(0)}%` : "—"}
                              {d.state === "estimate" && index != null ? <span style={{ fontSize: 11, fontWeight: 700, color: "#7a5a20" }}> est.</span> : null}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: "#4f6a4c", marginTop: 2 }}>
                            {d.state === "sofar" && d.toDate != null
                              ? `${d.toDate.toFixed(0)}% of normal to date${d.actual != null && d.normalSoFar != null ? `: ${d.actual.toFixed(2)}″ vs ${d.normalSoFar.toFixed(2)}″ normal to date` : ""} · `
                              : index != null && d.actual != null && d.normal != null ? `${d.actual.toFixed(2)}″ of ${d.normal.toFixed(2)}″ normal · ` : ""}
                            {Math.round(iv.pct * 100)}% of value · ${Math.round(covPerAc).toLocaleString()}/ac coverage
                            {estIndemAc != null && estIndemAc > 0 ? <b style={{ color: "#b03030" }}> · est. ${Math.round(estIndemAc).toLocaleString()}/ac</b> : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!isMobile && <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: isMobile ? 11.5 : 12.5 }}>
                    <thead>
                      <tr style={{ color: "#60735f", textAlign: "right", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                        <th style={{ textAlign: "left", padding: "4px 6px" }}>Interval</th>
                        <th style={{ padding: "4px 6px" }}>% Value</th>
                        <th style={{ padding: "4px 6px" }}>Normal</th>
                        <th style={{ padding: "4px 6px" }}>Received</th>
                        <th style={{ padding: "4px 6px" }}>% of Normal</th>
                        <th style={{ padding: "4px 6px" }}>Cov/ac</th>
                        {!isPortal && <th style={{ padding: "4px 6px" }}>Est. Indem/ac</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {fieldCov.codes.map(c => {
                        const iv = fieldCov.ivs[c];
                        const d = readOf(c);
                        const covPerAc = iv.protPerAc * iv.pct;
                        const index = d && d.index != null ? d.index : null;
                        const isFinal = d && d.isFinal;
                        // Coverage level MUST be a valid fraction (PRF is 0.70–0.90). Guard
                        // against bad data — e.g. the 150% productivity factor mis-stored as
                        // coverage — so an above-normal index can NEVER render a false payout.
                        const cl = (fieldCov.coverageLevel != null && fieldCov.coverageLevel > 0 && fieldCov.coverageLevel <= 1) ? fieldCov.coverageLevel
                                 : (coverage != null && coverage > 0 && coverage <= 1) ? coverage : null;
                        const covPct = cl != null ? cl * 100 : null;
                        const below = index != null && covPct != null && index < covPct;
                        // Pays only when the index is BELOW the coverage level; max(0,…) makes
                        // above-normal (index ≥ coverage) always $0.
                        const payFactor = (index != null && cl) ? Math.max(0, (cl - index / 100) / cl) : null;
                        const daysToEnd = _prfDaysToEnd(c, year);
                        // Est. indemnity only within 15 days of interval close (or RMA-final).
                        const gated = isFinal || (daysToEnd != null && daysToEnd <= 15);
                        const estIndemAc = (gated && payFactor != null) ? covPerAc * payFactor : null;
                        return (
                          <tr key={c} style={{ borderTop: "1px solid #eef2ee", textAlign: "right", color: "#2d4a2d" }}>
                            <td style={{ textAlign: "left", padding: "6px 6px", fontWeight: 700 }}>
                              {WXP.LABEL[c] || c}
                              {isFinal ? <span style={{ marginLeft: 5, fontSize: 9.5, fontWeight: 800, color: "#2d6a2d", background: "#e3f3e0", padding: "1px 4px", borderRadius: 5 }}>FINAL</span>
                                : d.state === "estimate" ? <span style={{ marginLeft: 5, fontSize: 9.5, fontWeight: 800, color: "#7a5a20", background: "#fbf2e0", padding: "1px 4px", borderRadius: 5 }}>EST</span> : null}
                            </td>
                            <td style={{ padding: "6px 6px" }}>{Math.round(iv.pct * 100)}%</td>
                            {/* Open (or closed short of its last day): rain so far against the
                                normal for the SAME days, never the whole interval's normal. */}
                            <td style={{ padding: "6px 6px" }}>{d.state === "sofar"
                              ? (d.normalSoFar != null ? <>{d.normalSoFar.toFixed(2)}″<span style={{ fontSize: 10, color: "#60735f" }}> to date</span></> : "—")
                              : d.normal != null ? `${d.normal.toFixed(2)}″` : "—"}</td>
                            <td style={{ padding: "6px 6px" }}>{(index != null || d.state === "sofar") && d.actual != null ? `${d.actual.toFixed(2)}″` : "—"}</td>
                            <td style={{ padding: "6px 6px", fontWeight: 800, color: index != null ? (below ? "#b03030" : index < 100 ? "#c07a1a" : "#2d7a3a") : d.state === "sofar" ? "#5b6f8a" : "#677167" }}>
                              {index != null ? <>{index.toFixed(0)}%{d.state === "estimate" ? <span style={{ fontSize: 10, fontWeight: 700, color: "#7a5a20" }}> est.</span> : null}</>
                                : d.state === "sofar" && d.toDate != null ? <>{d.toDate.toFixed(0)}%<span style={{ fontSize: 10, fontWeight: 600, color: "#60735f" }}> of normal to date</span></>
                                : <span style={{ fontWeight: 600 }}>{_prfStateLabel(d, year)}</span>}
                            </td>
                            <td style={{ padding: "6px 6px" }}>${Math.round(covPerAc).toLocaleString()}</td>
                            {!isPortal && <td style={{ padding: "6px 6px", fontWeight: estIndemAc > 0 ? 800 : 500, color: estIndemAc > 0 ? "#b03030" : "#677167" }}>
                              {estIndemAc != null ? `$${Math.round(estIndemAc).toLocaleString()}` : (index != null && !gated ? <span title="shown within 15 days of interval close">—</span> : "—")}
                            </td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>}
                <div style={{ fontSize: 10, color: "#677167", marginTop: 6, lineHeight: 1.5 }}>
                  {isPortal
                    ? <>Coverage/ac = protection/ac × % value. An interval pays when its index falls below your coverage level; your agent confirms any payment. Rainfall is this grid's official NOAA/RMA data.</>
                    : <>Coverage/ac = protection/ac × % value. Est. indemnity shown only within <b>15 days</b> of an interval's close (RMA‑final bypasses). Rainfall is this grid's official NOAA/RMA data.</>}
                </div>
              </div>
            );
          })()}

          {/* chosen intervals — the stars. Single-block grids render one clean list;
              multi-block grids render each acreage block's own 100% split (accurate,
              instead of merging blocks into >100%). Fallback only when we have no
              field_prf_intervals row for this field yet. */}
          {!fieldCov && chosenCodes.length > 0 && blocks.length <= 1 && (
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", marginBottom: 12 }}>Your insured intervals ({chosenCodes.length})</div>
              {chosenCodes.map(c => <RainBar key={c} code={c} big cov={chosen[c]} />)}
            </div>
          )}
          {!fieldCov && chosenCodes.length > 0 && blocks.length > 1 && (
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", marginBottom: 4 }}>How this grid is insured — {blocks.length} coverage blocks</div>
              <div style={{ fontSize: 11, color: "#7a5a20", background: "#fbf6ec", border: "1px solid #e7d4a8", borderRadius: 8, padding: "6px 10px", marginBottom: 12, lineHeight: 1.5 }}>
                This policy splits grid {grid} into {blocks.length} acreage blocks with different interval allocations. Your field is in <b>one</b> of them{isPortal ? "" : <> — the exact block comes from R&amp;H's per‑field data (coming)</>}. Rainfall is the same for every block (it's the grid's).
              </div>
              {blocks.map((b, bi) => (
                <div key={bi} style={{ marginBottom: 14, paddingLeft: 10, borderLeft: "3px solid #cdd9cd" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#2d4a2d", marginBottom: 8 }}>
                    Block {bi + 1} · {b.acres ? `${Number(b.acres).toFixed(2)} ac` : "—"}
                    <span style={{ fontWeight: 600, color: "#60735f" }}> · {b.codes.map(c => `${WXP.LABEL[c] || c} ${Math.round((b.intervals[c].pct || 0) * 100)}%`).join(" · ")}</span>
                  </div>
                  {b.codes.map(c => <RainBar key={c} code={c} cov={b.intervals[c]} />)}
                </div>
              ))}
            </div>
          )}

          {/* Field-specific: when we know how THIS field is insured, show ONLY those
              intervals. The full-grid view is a fallback shown only when the field has
              no insured intervals on file — and only to agents (prospecting a grid). */}
          {!fieldCov && chosenCodes.length === 0 && otherCodes.length > 0 && !isPortal && (
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#4a6e4a", marginBottom: 10 }}>All intervals in this grid <span style={{ fontWeight: 600, color: "#677167" }}>· this field has no insured intervals on file</span></div>
              {otherCodes.map(c => <RainBar key={c} code={c} />)}
            </div>
          )}
          {!fieldCov && chosenCodes.length === 0 && isPortal && (
            <div style={{ ...card, marginBottom: 12, color: "#7a5a20", background: "#fbf6ec", borderColor: "#e7d4a8", lineHeight: 1.6 }}>
              We don’t have insured PRF intervals on file for this field’s grid ({grid}) yet.
            </div>
          )}

          <div style={{ fontSize: 11, color: "#677167", lineHeight: 1.6, padding: "0 4px" }}>
            This is the <b>same NOAA rainfall data RMA uses to settle PRF</b> for grid {grid}. Estimated values track within ~1–3% of the eventual RMA Final. An interval pays when its index falls below your coverage level{isPortal ? "." : <>; payout ≈ liability × (coverage − index) ÷ coverage.</>}
          </div>
        </>
      )}
    </div>
  );
}

function FieldWeatherView({ field, isMobile = false, onBack, hideBack = false, portal = false }) {
  // field: { farmer, crop, fsn, tract, field_id, county, clu_id, latitude, longitude }
  const [cells, setCells]   = useState(null);   // [{id,lat,lon}]
  const [center, setCenter] = useState(null);   // [lat,lon] of the field
  const [rows, setRows]     = useState(null);    // weather rows for the matched cell
  const [err, setErr]       = useState(null);
  const [latestObs, setLatestObs] = useState(null); // window-independent latest obs_date → pipeline freshness
  const [clim, setClim]     = useState(null);    // doy → the cell's stored normal {p mm, g, hi °C, lo °C}
  const [showNormal, setShowNormal] = useState(false);   // overlay the 1995–2025 average
  // TODAY IN THE VIEWER'S OWN CALENDAR. Taken in UTC, every evening in New York read
  // as tomorrow. The Date object stays UTC-midnight of that local day so the date
  // arithmetic below is unchanged.
  const todayIso = wxsTodayIso();
  const today = useMemo(() => new Date(todayIso + "T00:00:00Z"), [todayIso]);
  const iso = (d) => d.toISOString().slice(0, 10);
  const yr = today.getUTCFullYear();
  // The crop year the field was opened from (the map's year picker) names the season.
  const cy = Number(field.cropYear) || yr;
  const seasonW = WXF.seasonWindow(cy, todayIso);
  // Plant date for the field, if reported (report_date) — drives the "Since
  // planting" preset, which is the default when a field is planted.
  //
  // ⚠ ONLY A PLANTING IN THE SEASON UNDER WAY. Opened from the 2025 map, this ran
  // "Since planting" from the 2025 plant date to TODAY — two seasons of rain and GDD
  // summed as one, a growth stage read off that total, and a "Data starts" banner
  // for a window nobody chose. A past crop year's planting is not this season's, and
  // nor is a date more than ~13 months back (fall wheat is the longest real gap).
  const plantRaw = field.planted && /^\d{4}-\d{2}-\d{2}/.test(field.planted) ? field.planted.slice(0, 10) : null;
  const pastCropYear = field.cropYear != null && Number(field.cropYear) < yr;
  // wxField.plantDateFor: this crop year's planting, fall-seeded wheat included, and
  // never a date past today. A past crop year keeps no "Since planting" here.
  const plantISO = !pastCropYear ? WXF.plantDateFor(plantRaw, cy, iso(today)) : null;
  const PRESETS = [
    ...(plantISO ? [{ key:"plant", label:`Since planting (${plantISO.slice(5).replace("-","/")})`, from: plantISO, to: iso(today) }] : []),
    // The crop year's season, Apr 1 to Nov 30 (the same window as the Weather page),
    // offered once it has started.
    ...(seasonW.to ? [{ key:"season", label:`${cy} season`, from: seasonW.from, to: seasonW.open ? iso(today) : seasonW.to }] : []),
    { key:"ytd",     label:`${yr} to date`, from: `${yr}-01-01`, to: iso(today) },
    { key:"d30",     label:"Last 30 days", from: iso(new Date(today.getTime() - 29*864e5)), to: iso(today) },
    { key:"d90",     label:"Last 90 days", from: iso(new Date(today.getTime() - 89*864e5)), to: iso(today) },
  ];
  // Opens on Since planting, else the crop year's season (so a field opened from the
  // 2025 map shows 2025), else this year to date.
  const firstPreset = PRESETS[0];
  const [preset, setPreset] = useState(firstPreset.key);
  const [from, setFrom] = useState(firstPreset.from);
  const [to, setTo]     = useState(firstPreset.to);
  const [showTable, setShowTable] = useState(false);
  const applyPreset = (k) => { const p = PRESETS.find(x => x.key === k); if (p) { setPreset(k); setFrom(p.from); setTo(p.to); } };

  // resolve the field's [lat,lon] — explicit (R&H) or the CLU centroid (NAU)
  useEffect(() => {
    let alive = true;
    if (field.latitude != null && field.longitude != null) { setCenter([Number(field.latitude), Number(field.longitude)]); }
    else if (field.clu_id) {
      sbFetch("field_geometries", { select:"centroid_lat,centroid_lon", filters:[`clu_id=ilike.${field.clu_id}`] })
        .then(d => { if (!alive) return; const g = d && d[0]; if (g && g.centroid_lat != null) setCenter([Number(g.centroid_lat), Number(g.centroid_lon)]); else setCenter(null); })
        .catch(() => { if (alive) setCenter(null); });
    } else setCenter(null);
    sbFetch("wny_grid_cells", { select:"id,lat,lon" })
      .then(c => { if (alive) setCells((c||[]).map(x => ({ id:x.id, lat:Number(x.lat), lon:Number(x.lon) }))); })
      .catch(() => { if (alive) setCells([]); });
    return () => { alive = false; };
  }, [field]);

  // nearest grid cell to the field centroid: the same snap rule as the Weather tab
  const nearest = useMemo(() => (cells && center ? WXF.nearestCell(center, cells) : null), [cells, center]);
  const tooFar = !!(nearest && nearest.far);   // beyond the edge of the grid

  // pull the daily observed weather for the matched cell over [from,to]
  useEffect(() => {
    let alive = true;
    if (!nearest || tooFar) { setRows(nearest ? [] : null); return; }
    setRows(null);
    sbFetch("wny_grid_weather_daily", {
      select: "obs_date,tmax_c,tmin_c,precip_mm,precip_gridmet_mm,precip_mrms_mm,source,is_forecast",
      filters: [`cell_id=eq.${nearest.id}`, `obs_date=gte.${from}`, `obs_date=lte.${to}`, `is_forecast=eq.false`],
      order: "obs_date.asc",
    }).then(d => { if (alive) setRows(d || []); }).catch(e => { if (alive) { setErr(e.message); setRows([]); } });
    return () => { alive = false; };
  }, [nearest, tooFar, from, to]);

  // Latest observed day for this cell regardless of the selected window — the true
  // "is the weather pipeline fresh?" signal (windowed rows can end in the past).
  useEffect(() => {
    let alive = true;
    if (!nearest || tooFar) { setLatestObs(null); return; }
    sbFetch("wny_grid_weather_daily", {
      select: "obs_date",
      filters: [`cell_id=eq.${nearest.id}`, `is_forecast=eq.false`],
      order: "obs_date.desc", limit: 1,
    }).then(d => { if (alive) setLatestObs(d && d[0] ? d[0].obs_date : null); }).catch(() => { if (alive) setLatestObs(null); });
    return () => { alive = false; };
  }, [nearest, tooFar]);

  // 30-yr normal for the matched cell, keyed by day-of-year (for the "vs average" overlay)
  useEffect(() => {
    let alive = true;
    if (!nearest || tooFar) { setClim(null); return; }
    sbFetch("wny_grid_climatology", { select:"cell_id,day_of_year,precip_mm,tmax_c,tmin_c,gdd_f,year_count", filters:[`cell_id=eq.${nearest.id}`] })
      .then(d => { if (alive) setClim(WXF.climoMap(d || []).byCell[nearest.id] || {}); })
      .catch(() => { if (alive) setClim({}); });
    return () => { alive = false; };
  }, [nearest, tooFar]);

  // The daily series (src/weather/wxField.js fieldSeries). Every calendar day from the
  // first row to the last is in it, and a day with no reading is null, so the charts
  // show a gap instead of closing it up and the totals never add a missing day as 0.
  // Rain is gridMET only, because the normal it is set against is gridMET.
  const series = useMemo(() => WXF.fieldSeries({ rows: rows || [], climo: clim, from, to }), [rows, clim, from, to]);

  // GDD since THIS field's planting date — the only accumulation a growth stage can
  // be read from. Deliberately independent of the selected preset: "Last 30 days"
  // accumulates 30 days of GDD, which is not a season total and would put the crop
  // in the wrong stage. If the window starts after planting we say so instead of
  // showing a stage we can't stand behind.
  const stageGdd = useMemo(() => {
    if (!plantISO) return { unavailable: pastCropYear ? `this is the ${field.cropYear} field map, so there is no planting in this season to read a stage from.`
      : plantRaw ? "the reported planting date isn't in this season." : "no planting date reported for this field." };
    if (from > plantISO) return { unavailable: `the selected window starts after the ${plantISO.slice(5).replace("-","/")} planting date — switch to “Since planting” to read stage.` };
    if (!rows || !rows.length) return { unavailable: "no observed weather in this window yet." };
    const g = WXF.gddSincePlant(series, plantISO);
    return g == null ? { unavailable: "no temperature readings since planting yet." } : { gdd: g };
  }, [plantISO, from, rows, series]); // eslint-disable-line

  // The normal over the displayed days, from the same series: it advances only on the
  // days the actual has a reading, so actual and normal always cover the same days.
  const normal = useMemo(() => {
    if (!clim || !series.dates.length || series.normP == null) return null;
    return { pD: series.nPD, pC: series.nPC, gD: series.nGD, gC: series.nGC, hi: series.nHi, lo: series.nLo, totP: series.normP, totG: series.normG || 0 };
  }, [clim, series]);
  const hasNormal = showNormal && !!normal;

  // Selected range starts before the first day this cell has on file → the totals
  // cover less than the range, and the banner says so.
  const coverageGap = series.covStart && from && series.covStart > from;
  const fmtMD = (d) => d ? `${d.slice(5,7)}/${d.slice(8,10)}/${d.slice(0,4)}` : "";
  // CSV of the daily detail (dependency-free).
  const downloadCsv = () => {
    const head = ["Date","High_F","Low_F","Precip_in","CumPrecip_in","GDD","CumGDD"];
    const lines = series.dates.map((d,i) => [d,
      series.hi[i]==null?"":Math.round(series.hi[i]*10)/10,
      series.lo[i]==null?"":Math.round(series.lo[i]*10)/10,
      series.precip[i]==null?"":Math.round(series.precip[i]*100)/100,
      Math.round(series.cumPrecip[i]*100)/100,
      series.gdd[i]==null?"":Math.round(series.gdd[i]*10)/10,
      Math.round(series.cumGdd[i]),
    ].join(","));
    const blob = new Blob([[head.join(","), ...lines].join("\n")], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const who = `${(field.farmer||"field").replace(/[^a-z0-9]+/gi,"_")}-${field.fsn||""}-${field.field_id||""}`.replace(/_+/g,"_");
    a.href = url; a.download = `weather-${who}-${from}_to_${to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  const card = { background:"#fff", border:"1px solid #d9e4d9", borderRadius:10, padding:"12px 14px" };
  const f1 = (v, s="") => v == null ? "—" : `${Math.round(v*10)/10}${s}`;

  const sel = { padding:"7px 10px", borderRadius:8, border:"1px solid #cdd9cd", background:"#fff", fontSize:13, color:"#1a2e1a" };
  const onCustom = (which, val) => { setPreset("custom"); which === "from" ? setFrom(val) : setTo(val); };
  const sectionH = (t, sub) => (
    <div style={{ margin:"4px 0 6px" }}>
      <div style={{ fontSize:13, fontWeight:800, color:"#1a2e1a" }}>{t}</div>
      {sub && <div style={{ fontSize:11, color:"#60735f" }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ maxWidth:1100, margin:"0 auto" }}>
      {!hideBack && <button onClick={onBack} style={{ ...sel, cursor:"pointer", fontWeight:700, color:"#2d4a2d", marginBottom:10 }}>← Back to Field Mapper</button>}
      <div style={{ ...card, marginBottom:12 }}>
        {!hideBack && <div style={{ fontSize:isMobile?18:22, fontWeight:800, color:"#1a2e1a" }}>{field.farmer || "Field"} — Weather</div>}
        {!hideBack && field.label && <div style={{ fontSize:14, fontWeight:800, color:"#2d6a1a", marginTop:2 }}>{field.label}</div>}
        <div style={{ fontSize:12.5, color:"#4a6e4a", marginTop:2 }}>
          {jdCropLabel(field.crop) === JD_CROP_UNSET ? JD_CROP_UNSET : fmCropName(field.crop, field.type_code)} · Farm {field.fsn || "—"} · Tract {field.tract || "—"} · Field {field.field_id || "—"}{field.county ? ` · ${fmTitleCase(field.county)} County` : ""}
          {plantISO && <span style={{ color:"#2d6a1a", fontWeight:700 }}> · Planted {plantISO.slice(5).replace("-","/")}/{plantISO.slice(0,4)}</span>}
        </div>
        {nearest && !tooFar && (
          <div style={{ fontSize:11, color:"#60735f", marginTop:4 }}>
            {portal
              ? <>Weather for this field’s location · {nearest.miles < 0.6 ? "at the field" : `${nearest.miles.toFixed(1)} mi from the field’s center`}</>
              : <>Grid cell #{nearest.id} · {nearest.miles < 0.6 ? "at the field" : `${nearest.miles.toFixed(1)} mi from field center`} · ~4 km gridMET, against its {WXF.NORMAL_LABEL}</>}
            {latestObs && <> · <FreshAsOf date={latestObs} label="observed weather" prefix="through" style={{ display:"inline" }} /></>}
          </div>
        )}
      </div>

      {/* date range */}
      <div style={{ ...card, display:"flex", flexWrap:"wrap", gap:8, alignItems:"center", marginBottom:12 }}>
        <div style={{ display:"flex", gap:4, background:"#eef3ee", borderRadius:8, padding:3, flexWrap:"wrap" }}>
          {PRESETS.map(p => (
            <button key={p.key} onClick={()=>applyPreset(p.key)} style={{ border:"none", borderRadius:6, padding: isMobile ? "0 12px" : "6px 12px", minHeight: isMobile ? 40 : undefined, fontSize: isMobile ? 13 : 12, fontWeight:800, cursor:"pointer", fontFamily:"inherit",
              background: preset===p.key ? "#1a2e1a" : "transparent", color: preset===p.key ? "#a8d878" : "#4a6e4a" }}>{p.label}</button>
          ))}
        </div>
        {/* 30-yr average (normal) overlay toggle */}
        <button onClick={()=>setShowNormal(s=>!s)} disabled={!normal}
          title={normal ? "Overlay the 1995–2025 average (normal) for this field's location" : "Normal not available for this field yet"}
          style={{ ...sel, cursor: normal?"pointer":"default", padding:"5px 11px", minHeight: isMobile ? 40 : undefined, fontWeight:800, fontSize: isMobile ? 13 : 12, display:"flex", alignItems:"center", gap:8,
            background:"#fff", color: showNormal?"#1a2e1a":(normal?"#4a6e4a":"#bcc8bc"), borderColor: showNormal?"#1a2e1a":"#cdd9cd" }}>
          <span style={{ width:26, height:15, borderRadius:8, background: showNormal?"#2d6a1a":"#cdd9cd", position:"relative", display:"inline-block", transition:"background .15s" }}>
            <span style={{ position:"absolute", top:2, left: showNormal?13:2, width:11, height:11, borderRadius:"50%", background:"#fff", transition:"left .15s" }} />
          </span>
          Compare to average
        </button>
        <div style={{ display:"flex", gap:6, alignItems:"center", marginLeft:"auto" }}>
          <input type="date" value={from} max={to} onChange={e=>onCustom("from", e.target.value)} style={{ ...sel, padding:"6px 8px", ...(isMobile ? { fontSize:16, minHeight:40 } : {}) }} />
          <span style={{ color:"#60735f", fontSize:12 }}>to</span>
          <input type="date" value={to} min={from} max={iso(today)} onChange={e=>onCustom("to", e.target.value)} style={{ ...sel, padding:"6px 8px", ...(isMobile ? { fontSize:16, minHeight:40 } : {}) }} />
        </div>
      </div>

      {/* where the crop is right now — GDD since this field's own planting date */}
      {/* Only for a crop the stage model is calibrated for (corn, soybeans). A PRF
          pasture or a hay field printed "No calibrated growth-stage model for prf" —
          a model's limits read out to someone who asked about the weather. */}
      {center != null && !tooFar && MI_STAGE_HAS_TABLE(field.crop) && (
        <div style={{ marginBottom:12 }}>
          <StageRail crop={field.crop} gdd={stageGdd.gdd} plantISO={plantISO} unavailable={stageGdd.unavailable} />
        </div>
      )}

      {center == null && cells ? (
        <div style={{ ...card, color:"#54724f" }}>No location on file for this field, so weather can’t be matched.{portal ? "" : " (It needs a CLU boundary centroid or R&H lat/lon.)"}</div>
      ) : tooFar ? (
        <div style={{ ...card, color:"#54724f", lineHeight:1.6 }}>
          {portal
            ? <><b style={{ color:"#1a2e1a" }}>Weather for this field isn’t available yet.</b> The nearest weather point is {nearest.miles.toFixed(0)} mi away; this page covers New York and Pennsylvania.</>
            : <><b style={{ color:"#1a2e1a" }}>Outside the weather grid.</b> The nearest grid cell is {nearest.miles.toFixed(0)} mi away; the ~4 km grid covers New York and Pennsylvania. This field’s weather isn’t available.</>}
        </div>
      ) : rows == null ? (
        <div style={{ ...card, color:"#60735f" }}><UiSpinner />Loading weather…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...card, color:"#54724f", lineHeight:1.6 }}>
          <b style={{ color:"#1a2e1a" }}>No data in this range.</b>{" "}
          {portal
            ? <>Nothing is on file for {fmtMD(from)} to {fmtMD(to)} yet. The newest day usually arrives a day or two late.</>
            : <>No observed rows for this cell from {fmtMD(from)} to {fmtMD(to)}.{latestObs ? ` The newest observed day is ${fmtMD(latestObs)}.` : ""} The newest day usually arrives a day or two late.</>}
        </div>
      ) : (
        <>
          {/* coverage banner — selected range starts before the loaded archive */}
          {coverageGap && (
            <div style={{ background:"#fdf5e6", border:"1px solid #e8d3a8", borderRadius:10, padding:"9px 12px", marginBottom:12, fontSize:12.5, color:"#7a5a1a", lineHeight:1.5 }}>
              <b>Data starts {fmtMD(series.covStart)}.</b>{" "}
              {portal
                ? <>You picked {fmtMD(from)}, but weather on file doesn’t go back that far. </>
                : <>You selected {fmtMD(from)}, but this cell’s rows start on {fmtMD(series.covStart)}. </>}
              <b>Every number below covers {fmtMD(series.covStart)}–{fmtMD(series.covEnd)} only</b> (so e.g. winter freeze days aren’t counted yet).
            </div>
          )}

          {/* The hero, in the Weather page's look: rain over the range against the same
              cell's normal for the same days, then heat, cold and GDD. */}
          {(() => { const rp = hasNormal && normal.totP > 0 ? Math.round(series.totPrecip / normal.totP * 100) : null; return (
          <WxHero isMobile={isMobile}
            eyebrow={`Rain · ${fmtMD(series.covStart)} to ${fmtMD(series.covEnd)} · ${series.nDays} days`}
            value={series.totPrecip != null ? (Math.round(series.totPrecip * 100) / 100).toFixed(2) : "—"} unit="in"
            pill={rp != null ? `${rp}% of normal` : null} pillTone={wxvPctTone(rp)}
            line={[hasNormal ? `Normal for the same days ${f1(normal.totP, " in")}.` : null, series.wettest ? `Wettest day ${f1(series.wettest, " in")}.` : null].filter(Boolean).join(" ") || null}
            stats={[
              { v: `${Math.round(series.totGdd).toLocaleString()} GDD`, l: hasNormal ? `base 50, ${series.totGdd-normal.totG>=0?"+":"−"}${Math.abs(Math.round(series.totGdd-normal.totG)).toLocaleString()} vs normal` : "base 50, 86° cap" },
              { v: `${f1(series.avgHi)}° / ${f1(series.avgLo)}°`, l: `average high / low · peak ${f1(series.maxHi)}°, min ${f1(series.minLo)}°` },
              { v: String(series.heat), l: "heat days over 90°F", tone: series.heat ? WXV_TONE.heat[0] : null },
              { v: String(series.freeze), l: "freeze days under 32°F", tone: series.freeze ? WXV_TONE.frost[0] : null },
            ]} />
          ); })()}

          {/* precipitation */}
          <div style={{ ...card, marginBottom:12 }}>
            {sectionH("Precipitation", `Daily rainfall (bars) and cumulative total (line) · ${f1(series.totPrecip,'"')} over the range${hasNormal?` · normal ${f1(normal.totP,'"')}`:""}`)}
            {/* Name the instrument, the gaps and the newest days, so the total and its
                normal are read as what they are: one source, the same days. */}
            <div style={{ fontSize:11, color:"#60735f", margin:"-2px 0 8px" }}>
              gridMET rain{series.lastGm ? ` through ${fmtMD(series.lastGm)}` : ""}, the same source as the 1995–2025 normal.
              {series.radarTail != null ? ` Radar since then: ${series.radarTail.toFixed(2)}" over ${series.radarTailDays} day${series.radarTailDays>1?"s":""}, not yet in the total.` : ""}
              {series.missing - (series.radarTailDays || 0) > 0 ? ` ${series.missing - (series.radarTailDays || 0)} day${series.missing - (series.radarTailDays || 0) > 1 ? "s" : ""} with no reading left out of both totals.` : ""}
            </div>
            <WxChart days={series.dates} bars={series.precip} missing={series.precip.map(v => v == null)}
              lines={[{ data: series.cumPrecip, color:"#1f5fb0", width:2, axis2:true },
                      ...(hasNormal ? [{ data: normal.pC, color:"#677167", width:1.6, axis2:true, dash:"5,3" }] : [])]}
              color="#5b9bd5" yLabel='in/day' y2Label='Total' height={isMobile?160:190} fmtY={(v)=>v.toFixed(2)} fmtY2={(v)=>v.toFixed(1)+'"'}
              tooltip={(i)=>[{ label:"Rain", value: series.precip[i]==null?"—":`${(Math.round(series.precip[i]*100)/100).toFixed(2)}"`, color:"#5b9bd5" },
                             { label:"Total", value:`${series.cumPrecip[i].toFixed(2)}"`, color:"#1f5fb0" },
                             ...(hasNormal ? [{ label:"Normal", value:`${normal.pC[i].toFixed(2)}"`, color:"#677167" }] : [])]} />
          </div>

          {/* temperature */}
          <div style={{ ...card, marginBottom:12 }}>
            {sectionH("Temperature", "Daily high–low range (band) with 90°F heat and 32°F freeze references")}
            <WxChart days={series.dates} bands={series.bands} zero={false}
              lines={[{ data: series.hi, color:"#b1442e", width:1.5 }, { data: series.lo, color:"#1f5fb0", width:1.5 },
                      ...(hasNormal ? [{ data: normal.hi, color:"#caa6a0", width:1.3, dash:"4,3" }, { data: normal.lo, color:"#9ab0c4", width:1.3, dash:"4,3" }] : [])]}
              refLines={[{ v:90, color:"#b1442e", label:"90° heat" }, { v:32, color:"#1f7fb0", label:"32° freeze" }]}
              color="#d0954a" yLabel='°F' height={isMobile?170:200} fmtY={(v)=>Math.round(v)+"°"}
              tooltip={(i)=>[{ label:"High", value: series.hi[i]==null?"—":`${Math.round(series.hi[i]*10)/10}°`, color:"#b1442e" },
                             { label:"Low", value: series.lo[i]==null?"—":`${Math.round(series.lo[i]*10)/10}°`, color:"#1f5fb0" },
                             ...(hasNormal ? [{ label:"Norm hi", value: normal.hi[i]==null?"—":`${Math.round(normal.hi[i])}°`, color:"#caa6a0" },
                                              { label:"Norm lo", value: normal.lo[i]==null?"—":`${Math.round(normal.lo[i])}°`, color:"#9ab0c4" }] : [])]} />
          </div>

          {/* GDD */}
          <div style={{ ...card, marginBottom:12 }}>
            {sectionH("Growing degree days", `Daily GDD (bars) and accumulation (line) · ${Math.round(series.totGdd).toLocaleString()} GDD total${hasNormal?` · normal ${Math.round(normal.totG).toLocaleString()}`:""}${series.gddMissing ? ` · ${series.gddMissing} day${series.gddMissing>1?"s":""} with no temperature left out of both` : ""}`)}
            <WxChart days={series.dates} bars={series.gdd} missing={series.gdd.map(v => v == null)}
              lines={[{ data: series.cumGdd, color:"#2d6a1a", width:2, axis2:true },
                      ...(hasNormal ? [{ data: normal.gC, color:"#677167", width:1.6, axis2:true, dash:"5,3" }] : [])]}
              color="#86c95a" yLabel='GDD/day' y2Label='Total' height={isMobile?160:190} fmtY={(v)=>Math.round(v)} fmtY2={(v)=>Math.round(v).toLocaleString()}
              tooltip={(i)=>[{ label:"GDD", value: series.gdd[i]==null?"—":`${Math.round(series.gdd[i]*10)/10}`, color:"#86c95a" },
                             { label:"Total", value: Math.round(series.cumGdd[i]).toLocaleString(), color:"#2d6a1a" },
                             ...(hasNormal ? [{ label:"Normal", value: Math.round(normal.gC[i]).toLocaleString(), color:"#677167" }] : [])]} />
          </div>

          {/* daily detail table */}
          <div style={{ ...card, marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <div style={{ fontSize:13, fontWeight:800, color:"#1a2e1a", cursor:"pointer", flex:1 }} onClick={()=>setShowTable(s=>!s)}>
                <span style={{ color:"#60735f", marginRight:6 }}>{showTable?"▾":"▸"}</span>Daily detail <span style={{ fontWeight:500, color:"#60735f" }}>· {series.nDays} days</span>
              </div>
              <button onClick={downloadCsv} title="Download the daily table as CSV"
                style={{ ...sel, padding:"5px 10px", minHeight: isMobile ? 40 : undefined, cursor:"pointer", fontSize:12, fontWeight:700, color:"#2d4a2d" }}>Download CSV</button>
            </div>
            {showTable && (
              <div style={{ overflowX:"auto", marginTop:8, maxHeight:520, overflowY:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5, minWidth:560 }}>
                  <thead><tr style={{ position:"sticky", top:0, background:"#fff" }}>
                    {["Date","High","Low","Rain","Cum rain","GDD","Cum GDD"].map((h,i) => (
                      <th key={h} style={{ textAlign: i===0?"left":"right", padding:"6px 10px", borderBottom:"2px solid #d9e4d9", fontSize:10.5, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.04em", color:"#4a6e4a", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {series.dates.map((d,i) => {
                      const hot = series.hi[i]!=null && series.hi[i]>90, frz = series.lo[i]!=null && series.lo[i]<32;
                      const tdc = { padding:"4px 10px", borderBottom:"1px solid #f0f4f0", textAlign:"right", fontVariantNumeric:"tabular-nums" };
                      return (
                        <tr key={d}>
                          <td style={{ ...tdc, textAlign:"left", color:"#1f3320", fontWeight:600 }}>{fmtMD(d)}</td>
                          <td title={hot ? "Over 90°F" : undefined} style={{ ...tdc, color: hot?"#b1442e":"#1f3320", fontWeight: hot?700:400 }}>{series.hi[i]==null?"—":`${Math.round(series.hi[i]*10)/10}°`}{hot?" hot":""}</td>
                          <td title={frz ? "Below 32°F" : undefined} style={{ ...tdc, color: frz?"#1f5fb0":"#1f3320", fontWeight: frz?700:400 }}>{series.lo[i]==null?"—":`${Math.round(series.lo[i]*10)/10}°`}{frz?" frost":""}</td>
                          {/* A measured dry day reads 0.00"; a day with no reading says so. They
                              used to print the same dash. */}
                          <td title={series.precip[i]==null ? "No gridMET reading this day" : undefined} style={{ ...tdc, color: series.precip[i]>0?"#1f5fb0":"#bcc8bc", fontStyle: series.precip[i]==null?"italic":undefined }}>{series.precip[i]==null?"no reading":`${(Math.round(series.precip[i]*100)/100).toFixed(2)}"`}</td>
                          <td style={{ ...tdc, color:"#4a6e4a" }}>{`${series.cumPrecip[i].toFixed(2)}"`}</td>
                          <td style={tdc}>{series.gdd[i]==null?"—":Math.round(series.gdd[i]*10)/10}</td>
                          <td style={{ ...tdc, color:"#2d6a1a", fontWeight:600 }}>{Math.round(series.cumGdd[i]).toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {err && <div style={{ fontSize:11, color:"#b00" }}>Note: {err}</div>}
        </>
      )}
    </div>
  );
}


// WEATHER BY FIELD — per-field high-def weather, scoped to ONE selected farm (book-wide
// is too much to scan and nobody looks at it). Native ~1km gauge-corrected MRMS rainfall
// (field_precip_daily, per CLU, over a selectable window) + season GDD (base 50°F, from
// each field's nearest ~4km cell). Sortable columns + CSV export. Renders only when the
// current filter narrows to a single grower.
function FieldWeatherPanel({ rows, centroids, nameOf, cty, isMobile, onLocate, onPickFarm = null, portal = false, cropYear = null, onOpenField = null, onWeatherPage = null }) {
  const [days, setDays] = useState(7);        // 7 | 14 | 30 | 0 = season (since Apr 1)
  const [rainDaily, setRainDaily] = useState(null); // clu(lower) -> { 'YYYY-MM-DD': mm } (Apr 1 → to)
  const [gddDaily, setGddDaily]   = useState(null); // cell_id -> { 'YYYY-MM-DD': gdd_f } (Apr 1 → to)
  // cell_id -> { 'YYYY-MM-DD': gridMET mm }. The "% of normal" is computed on THIS, not
  // on the field's radar inches: the normal is built from gridMET, and radar read 2–9%
  // above gridMET across Genesee/Livingston/Wyoming in 2026, so radar ÷ gridMET-normal
  // reads wet by exactly that bias. Like with like, over the same days.
  const [gmDaily, setGmDaily]     = useState(null);
  const [cells, setCells] = useState(null);
  const [cellByClu, setCellByClu] = useState(null); // clu(lower) -> nearest wny cell_id
  const [climo, setClimo] = useState(null);    // cell_id -> { doy -> {p, g} }, the stored gridMET 1995–2025 normal
  const [err, setErr] = useState(null);
  const [labels, setLabels] = useState({});    // clu(lower) -> custom field name
  const [jdNames, setJdNames] = useState(null);// clu(lower) -> { name, via } from John Deere Ops Center
  const [jdInfo, setJdInfo] = useState(null);  // counts + misplaced-org diagnostic for the header
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [sortKey, setSortKey] = useState("rain");
  const [sortDir, setSortDir] = useState("desc");
  const canEdit = isCurrentUserAdmin();
  const card = { background:"#fff", border:"1px solid #d9e4d9", borderRadius:10, padding:"12px 14px" };
  const sel  = { padding:"6px 10px", borderRadius:8, border:"1px solid #cdd9cd", background:"#fff", fontSize:13, color:"#1a2e1a", cursor:"pointer" };

  // Weather is per-operation. Only render when the view resolves to ONE grower.
  const farmIds = useMemo(() => [...new Set(rows.map(r => r.farmer_id).filter(Boolean))], [rows]);
  const singleFarm = farmIds.length === 1;
  const farmName = singleFarm ? (nameOf(rows.find(r => r.farmer_id === farmIds[0])) || "this farm") : null;

  // one row per physical field (dedup by farmer|FSN-Tract-Field), with a resolvable CLU
  const fields = useMemo(() => {
    const seen = new Set(), out = [];
    for (const r of rows) {
      const clu = (r.clu_id || "").toLowerCase(); if (!clu) continue;
      const path = [r.fsn, r.tract, r.field_id].some(x => x != null) ? `${r.fsn}-${r.tract}-${r.field_id}` : clu;
      const key = `${r.farmer_id || ""}|${path}`;
      if (seen.has(key)) continue; seen.add(key);
      out.push({ key, clu, r, path });
    }
    return out;
  }, [rows]);

  // ── THE WINDOW (src/weather/wxField.js holds the rules; tools/check-wx-field.js
  // drives them). The season is the crop year the map is drawn for, Apr 1 to Nov 30,
  // ending at yesterday IN THE VIEWER'S OWN CALENDAR while it is under way. The 7/14/30
  // windows end on the same day. Opened on the 2025 map, the tab used to show 2026's
  // weather, and at 9 PM in New York "yesterday" was taken in UTC and read today.
  // Daily detail is fetched ONCE for the longest window any toggle needs and summed
  // client-side, so the toggle never refetches.
  const todayIso = wxsTodayIso();
  const yr = Number(cropYear) || CURRENT_CROP_YEAR;
  const season = useMemo(() => WXF.seasonWindow(yr, todayIso), [yr, todayIso]);
  const seasonFrom = season.from;
  const seasonStarted = season.to != null;   // false: this crop year's season is still ahead
  const to = season.to || WXF.yesterday(todayIso);
  const plantMode = days === "plant";
  // A field's real planting date for this crop year. Fall-seeded wheat is planted the
  // autumn before, and the old tab threw every such date away.
  const plantDateOf = (f) => WXF.plantDateFor(f.r.report_date, yr, to);
  const plantMin = useMemo(() => {
    let m = null; for (const f of fields) { const d = plantDateOf(f); if (d && (!m || d < m)) m = d; } return m;
  }, [fields, yr, to]); // eslint-disable-line
  const fetchFrom = useMemo(() => {
    let m = WXF.lastNFrom(to, 30);
    if (seasonStarted && seasonFrom < m) m = seasonFrom;
    if (plantMin && plantMin < m) m = plantMin;
    return m;
  }, [to, seasonStarted, seasonFrom, plantMin]);
  // sbFetch stops at 50 pages of 1,000 rows WITHOUT saying so, so each request is
  // sized to stay well under that however long the window (a fall-wheat "since plant"
  // runs ~15 months).
  const batchFor = (perDay) => Math.max(5, Math.min(120, Math.floor(40000 / Math.max(1, WXF.countDays(fetchFrom, to) * perDay))));
  const winFrom = (days === 0 || plantMode) ? (seasonStarted ? seasonFrom : null) : WXF.lastNFrom(to, days);
  const range = { from: winFrom, to };

  const locOf = (f) => {
    if (f.r.latitude != null && f.r.longitude != null) return [Number(f.r.latitude), Number(f.r.longitude)];
    const c = centroids && centroids[f.clu];   // [lon, lat]
    return c ? [c[1], c[0]] : null;
  };

  // daily rainfall per field (field_precip_daily, native 1km, per CLU). A null
  // reading stays out of the map: a missing day is not a dry day.
  useEffect(() => {
    if (!singleFarm) { setRainDaily(null); return; }
    let alive = true; setRainDaily(null); setErr(null);
    const clus = [...new Set(fields.map(f => f.clu))];
    if (!clus.length) { setRainDaily({}); return; }
    (async () => {
      const all = []; const step = batchFor(1);
      for (let i = 0; i < clus.length; i += step) {
        const batch = clus.slice(i, i + step);
        const rP = await sbFetch("field_precip_daily", { select: "clu_id,obs_date,precip_mm", filters: [`clu_id=in.(${batch.join(",")})`, `obs_date=gte.${fetchFrom}`, `obs_date=lte.${to}`] });
        for (const p of (rP || [])) all.push(p);
      }
      if (alive) setRainDaily(WXF.fieldRainMap(all));
    })().catch(e => { if (alive) { setErr(e.message); setRainDaily({}); } });
    return () => { alive = false; };
  }, [fields, fetchFrom, to, singleFarm]); // eslint-disable-line

  // the grid cell under each field (one snap rule for the tab and the field page),
  // then that cell's gridMET rain and GDD
  useEffect(() => {
    if (!singleFarm) { setGddDaily(null); setGmDaily(null); setCellByClu(null); return; }
    let alive = true; setGddDaily(null); setGmDaily(null);
    (async () => {
      let cs = cells;
      if (!cs) { cs = (await sbFetch("wny_grid_cells", { select:"id,lat,lon" })).map(x => ({ id:x.id, lat:Number(x.lat), lon:Number(x.lon) })); if (alive) setCells(cs); }
      const nearest = {}; const need = new Set();
      for (const f of fields) {
        const n = WXF.nearestCell(locOf(f), cs);
        if (n && !n.far) { nearest[f.clu] = n.id; need.add(n.id); }
      }
      if (alive) setCellByClu(nearest);       // shared with the normals fetch below
      const ids = [...need]; const all = []; const step = batchFor(1);
      for (let i = 0; i < ids.length; i += step) {
        const batch = ids.slice(i, i + step);
        const wd = await sbFetch("wny_grid_weather_daily", { select:"cell_id,obs_date,tmax_c,tmin_c,precip_mm,precip_gridmet_mm,source,is_forecast", filters:[`cell_id=in.(${batch.join(",")})`, `obs_date=gte.${fetchFrom}`, `obs_date=lte.${to}`, "is_forecast=eq.false"] });
        for (const d of (wd || [])) all.push(d);
      }
      const m = WXF.cellMaps(all);
      if (alive) { setGddDaily(m.gdd); setGmDaily(m.gm); }
    })().catch(e => { if (alive) { setErr(e.message); setGddDaily({}); setGmDaily({}); } });
    return () => { alive = false; };
  }, [fields, singleFarm, fetchFrom, to]); // eslint-disable-line

  // each cell's stored normal (day-of-year curve), windowed client-side
  useEffect(() => {
    if (!singleFarm || !cellByClu) { setClimo(null); return; }
    let alive = true;
    const ids = [...new Set(Object.values(cellByClu))];
    if (!ids.length) { setClimo({}); return; }
    (async () => {
      const all = [];
      for (let i = 0; i < ids.length; i += 100) {
        const batch = ids.slice(i, i + 100);
        const cr = await sbFetch("wny_grid_climatology", { select:"cell_id,day_of_year,precip_mm,gdd_f,year_count", filters:[`cell_id=in.(${batch.join(",")})`] });
        for (const x of (cr || [])) all.push(x);
      }
      if (alive) setClimo(WXF.climoMap(all).byCell);
    })().catch(e => { if (alive) { setErr(e.message); setClimo({}); } });
    return () => { alive = false; };
  }, [cellByClu, singleFarm]);

  // One field's totals over the selected window. Rain counts the field's own radar
  // days and names the days expected; "% of normal" is the field's grid cell, gridMET
  // over its gridMET normal on the same days. GDD runs from Apr 1 (or the field's own
  // planting in Since-plant), and its normal advances only on days with a reading.
  const stats = useMemo(() => {
    if (!rainDaily) return null;
    const out = {};
    for (const f of fields) {
      const cid = cellByClu ? cellByClu[f.clu] : null;
      const pd = plantDateOf(f);
      out[f.clu] = WXF.fieldTotals({
        fieldRain: rainDaily[f.clu] || null,
        cellGm: gmDaily && cid != null ? gmDaily[cid] || null : null,
        cellGdd: gddDaily && cid != null ? gddDaily[cid] || null : null,
        climo: climo && cid != null ? climo[cid] || null : null,
        rainFrom: plantMode ? pd : winFrom,
        gddFrom: plantMode ? pd : (seasonStarted ? seasonFrom : null),
        to,
      });
    }
    return out;
  }, [rainDaily, gmDaily, gddDaily, climo, cellByClu, fields, plantMode, winFrom, seasonStarted, seasonFrom, to]); // eslint-disable-line
  const gddLoaded = !!gddDaily && !!cellByClu;
  const pctOf = (act, nrm) => (act != null && nrm != null && nrm > 0) ? (act / nrm * 100) : null;
  const rainPctColor = (p) => p == null ? "#677167" : p < 75 ? "#b3701a" : p > 130 ? "#1f5fb0" : "#4a6e4a";
  const gddPctColor  = (p) => p == null ? "#677167" : p < 95 ? "#b3701a" : p > 105 ? "#1f5fb0" : "#3a6b2a";

  // custom field names (fall back to FSN-Tract-Field when unnamed)
  useEffect(() => {
    if (!singleFarm) { setLabels({}); return; }
    let alive = true;
    const clus = [...new Set(fields.map(f => f.clu))]; if (!clus.length) { setLabels({}); return; }
    (async () => {
      const acc = {};
      for (let i = 0; i < clus.length; i += 150) {
        const b = clus.slice(i, i + 150);
        const r = await sbFetch("field_labels", { select: "clu_id,label", filters: [`clu_id=in.(${b.join(",")})`] });
        for (const x of (r || [])) acc[(x.clu_id || "").toLowerCase()] = x.label;
      }
      if (alive) setLabels(acc);
    })().catch(() => {});
    return () => { alive = false; };
  }, [fields, singleFarm]);

  // The grower's OWN field names, when this farm is connected to John Deere Ops
  // Center. "6517-6201-2" is the FSA key; "CR OEH and Fisher Rd" is what they call
  // it on the phone, and a rain table is read by the second name. Resolved
  // server-side (jd_* tables are service-role only) by centroid-in-boundary — see
  // the long note on hFieldNames in api/_jd.js for what it refuses and why.
  //
  // Deliberately NOT written into field_labels: that table means "a human named
  // this field". Reading JD live instead keeps a rename in Ops Center showing up
  // here the same day and can never clobber a name Kevin or the grower typed.
  useEffect(() => {
    if (!singleFarm) { setJdNames(null); setJdInfo(null); return; }
    let alive = true; setJdNames(null); setJdInfo(null);
    authedFetch(`/api/market?fn=jd-field-names&farmer=${encodeURIComponent(farmIds[0])}`)
      .then(r => r.json())
      .then(d => { if (!alive) return; setJdNames((d && d.names) || {}); setJdInfo(d || null); })
      .catch(() => { if (alive) { setJdNames({}); setJdInfo(null); } });
    return () => { alive = false; };
  }, [singleFarm, farmIds]);

  // Precedence: a name a person typed always wins, then John Deere's, then the FSA
  // key. Never the other way round — an automatic name must not overwrite what the
  // agent or the grower deliberately called the field.
  const jdNameOf = (f) => (jdNames && jdNames[f.clu]) || null;
  const nameFor = (f) => labels[f.clu] || (jdNameOf(f) || {}).name || `${f.r.fsn || "—"}-${f.r.tract || "—"}-${f.r.field_id || "—"}`;
  const namedFromJd = (f) => !labels[f.clu] && !!jdNameOf(f);
  const saveLabel = async (f) => {
    const v = draft.trim(); setEditing(null);
    try {
      if (v) { await sbUpsert("field_labels", [{ clu_id: f.clu, farmer_id: f.r.farmer_id || null, label: v, source: "agent", updated_by: getCurrentUserEmail(), updated_at: new Date().toISOString() }], "clu_id"); setLabels(m => ({ ...m, [f.clu]: v })); }
      else if (labels[f.clu]) { await sbDelete("field_labels", `clu_id=eq.${encodeURIComponent(f.clu)}`); setLabels(m => { const n = { ...m }; delete n[f.clu]; return n; }); }
    } catch (_) {}
  };

  const toggleSort = (k) => { if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir(k === "field" || k === "crop" || k === "county" ? "asc" : "desc"); } };
  const display = useMemo(() => {
    if (!stats) return [];
    const arr = fields.map(f => { const st = stats[f.clu] || {}; return { ...f, mm: st.rainMm ?? null,
      gdd: gddLoaded ? (st.gdd ?? null) : null, gddNorm: st.gddNorm ?? null, gddOk: !!st.gddOk, gddDays: st.gddDays || 0, gddExp: st.gddExp || 0,
      rainNormMm: st.rainNormMm ?? null, rainAreaMm: st.areaMm ?? null, rainAreaNormMm: st.areaNormMm ?? null, rainPct: st.areaPct ?? null,
      rainOk: !!st.rainOk, rainDays: st.rainDays || 0, rainExp: st.rainExp || 0 }; });
    const sv = (f) => { switch (sortKey) {
      case "field":  return nameFor(f).toLowerCase();
      case "crop":   return (f.r.crop || "").toLowerCase();
      case "county": return (cty(f.r) || "").toLowerCase();
      case "gdd":    return f.gdd ?? -1;
      case "rainpct": return f.rainPct ?? -1;
      case "gddpct":  return pctOf(f.gdd, f.gddNorm) ?? -1;
      default:       return f.mm ?? -1;   // rain
    } };
    const dir = sortDir === "asc" ? 1 : -1, isStr = sortKey === "field" || sortKey === "crop" || sortKey === "county";
    return arr.sort((a, b) => { const va = sv(a), vb = sv(b); return (isStr ? String(va).localeCompare(String(vb)) : (va - vb)) * dir; });
  }, [fields, stats, gddLoaded, sortKey, sortDir, labels, jdNames]); // eslint-disable-line
  const withData = display.filter(f => f.mm != null);
  // KPI rain avg + % only over fields with COMPLETE precip coverage — a partial-backfill
  // field would drag the average down and produce a bogus "% of normal".
  const rainComplete = display.filter(f => f.mm != null && f.rainOk);
  const rainPartial = withData.length - rainComplete.length;
  const avgIn = rainComplete.length ? rainComplete.reduce((s, f) => s + f.mm, 0) / rainComplete.length / 25.4 : null;
  // GDD averages only fields whose cell reported most of the window, like rain does,
  // and its normal is the mean over those same fields.
  const gddVals = display.filter(f => f.gdd != null && f.gddOk);
  const gddPartial = display.filter(f => f.gdd != null && !f.gddOk).length;
  const avgGdd = gddVals.length ? gddVals.reduce((s, f) => s + f.gdd, 0) / gddVals.length : null;
  const rnVals = rainComplete.filter(f => f.rainNormMm != null);
  const avgRainNormIn = rnVals.length ? rnVals.reduce((s, f) => s + f.rainNormMm, 0) / rnVals.length / 25.4 : null;
  // The KPI's % is gridMET over its own normal (same days), summed across the
  // complete fields — not the radar average over a gridMET normal.
  const raVals = rainComplete.filter(f => f.rainAreaMm != null && f.rainAreaNormMm != null);
  const avgRainPct = raVals.length ? pctOf(raVals.reduce((s, f) => s + f.rainAreaMm, 0), raVals.reduce((s, f) => s + f.rainAreaNormMm, 0)) : null;
  const gnVals = gddVals.filter(f => f.gddNorm != null);
  const avgGddNorm = gnVals.length ? gnVals.reduce((s, f) => s + f.gddNorm, 0) / gnVals.length : null;
  // Freshness: latest observed day present across this farm's rainfall (and GDD)
  // feeds. If the nightly pipeline stalls, this stops advancing past yesterday.
  const dataThrough = useMemo(() => {
    let mx = null;
    for (const m of [rainDaily, gddDaily]) {
      if (!m) continue;
      for (const k in m) for (const d in m[k]) if (!mx || d > mx) mx = d;
    }
    return mx;
  }, [rainDaily, gddDaily]);

  const exportCsv = () => {
    const winTxt = plantMode ? "since plant" : `${range.from || "-"}..${range.to}`;
    const gddTxt = plantMode ? "GDD since plant" : "Season GDD";
    // "Name source" rides along so a grower comparing this to their own records can
    // see which rows we named ourselves and which came out of their Ops Center.
    // The field's rain is its own radar; "% of normal" is its grid cell's gridMET rain
    // over the gridMET normal on the same days. Two instruments, so two named columns.
    const hdr = ["Field", "Name source", "FSN-Tract-Field", "Crop", "County", "Planted", `Field rain ${winTxt} (in, radar)`, "Rain days present", "Normal over those days (in)", "Area rain % of normal (gridMET, same days)", `${gddTxt} (base 50F)`, "GDD days present", `${gddTxt} normal`, "GDD % of normal"];
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const lines = [hdr.map(esc).join(",")];
    for (const f of display) {
      const rp = f.rainOk ? f.rainPct : null, gp = pctOf(f.gdd, f.gddNorm);   // suppress % on incomplete rain
      const pd = f.r.report_date ? String(f.r.report_date).slice(0, 10) : "";
      const src = labels[f.clu] ? "named here" : namedFromJd(f) ? `John Deere (${jdNameOf(f).via === "ftf" ? "harvest attribution" : "boundary"})` : "FSA farm-tract-field";
      lines.push([nameFor(f), src, `${f.r.fsn || ""}-${f.r.tract || ""}-${f.r.field_id || ""}`, f.r.crop || "", cty(f.r) || "", pd,
        f.mm != null ? (f.mm / 25.4).toFixed(2) : "", f.mm != null ? `${f.rainDays}/${f.rainExp}` : "", f.rainNormMm != null ? (f.rainNormMm / 25.4).toFixed(2) : "", rp != null ? Math.round(rp) : "",
        f.gdd != null ? Math.round(f.gdd) : "", f.gdd != null ? `${f.gddDays}/${f.gddExp}` : "", f.gddNorm != null ? Math.round(f.gddNorm) : "", gp != null && f.gddOk ? Math.round(gp) : ""].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `${String(farmName || "farm").replace(/[^a-z0-9]+/gi, "_")}_field_weather_${range.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  // Gate: weather is per-farm — prompt to pick one until the view is a single grower.
  if (!farmIds.length) return <div style={{ ...card, color:"#60735f" }}>No insured fields in view.</div>;
  if (!singleFarm && onPickFarm) return <FmFarmChooser rows={rows} nameOf={nameOf} onPick={onPickFarm} what="Weather" />;
  if (!singleFarm) return (
    <div style={{ ...card, color:"#54724f", lineHeight:1.6 }}>
      <div style={{ fontWeight:800, color:"#1a2e1a", marginBottom:4 }}>Weather is shown one farm at a time.</div>
      Pick a farm — click a grower in the Acreage Summary tab, or search one above — to see that operation's fields: per-field rainfall, season GDD, and more, all sortable and exportable.
      <div style={{ fontSize:11.5, color:"#677167", marginTop:6 }}>{farmIds.length} farms in the current view.</div>
    </div>
  );

  const th = (label, k, right) => (
    <th onClick={()=>toggleSort(k)} style={{ textAlign:right?"right":"left", padding:"8px 12px", cursor:"pointer", userSelect:"none", whiteSpace:"nowrap" }}>
      {label}{sortKey===k ? (sortDir==="asc"?" ▲":" ▼") : ""}
    </th>
  );

  return (
    <div>
      <div style={{ ...card, marginBottom:12, display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        {!isMobile && <div style={{ fontSize:13, fontWeight:800, color:"#1a2e1a" }}>Weather by field</div>}
        {!isMobile && <span style={{ fontSize:12, fontWeight:700, color:"#2d4a2d" }}>· {farmName}</span>}
        <span style={{ fontSize:11.5, color:"#60735f" }}>{portal ? "rain measured at each field" : <>native ~1&nbsp;km gauge-corrected radar</>}</span>
        {dataThrough && <FreshAsOf date={dataThrough} label="rainfall/GDD" prefix="through" />}
        {/* Say how many rows are reading in the grower's own language, and say it even
            when the answer is none — a farm connected to John Deere whose fields name
            nothing looks identical to a farm that was never connected. */}
        {(() => {
          if (!jdInfo || !jdInfo.counts || !jdInfo.counts.jd_fields) return null;
          if (!display.length && !jdInfo.misplaced_org) return null;   // still loading the table
          const jdShown = display.filter(f => namedFromJd(f)).length;
          // The misplaced-org chip is a diagnostic for US ("wrong entity?" means the
          // agency mapped this farm to the wrong John Deere org). A grower can do
          // nothing with it, so they get no chip at all rather than a puzzle.
          if (jdInfo.misplaced_org && portal) return null;
          if (jdInfo.misplaced_org) return (
            <span title={jdInfo.misplaced_org.note} style={{ fontSize:11, fontWeight:700, color:"#b3701a" }}>
              {jdInfo.misplaced_org.jd_fields} John&nbsp;Deere fields ~{jdInfo.misplaced_org.nearest_jd_field_mi}&nbsp;mi away — wrong entity?
            </span>
          );
          return (
            <span title={`Matched by boundary: ${jdInfo.counts.boundary}. By harvest attribution: ${jdInfo.counts.ftf}.${jdInfo.counts.ambiguous ? ` Left unnamed because the field's centroid sits inside two differently-named JD boundaries: ${jdInfo.counts.ambiguous}.` : ""}`}
              style={{ fontSize:11, fontWeight:700, color: jdShown ? "#3a6b2a" : "#677167" }}>
              {jdShown} of {display.length} named from John&nbsp;Deere
            </span>
          );
        })()}
        <button onClick={exportCsv} title="Export this table to CSV" style={{ ...sel, cursor:"pointer", color:"#2d4a2d", fontWeight:700, padding:"5px 10px", minHeight: isMobile ? 40 : undefined }}>Export</button>
        {/* The forward-looking side (radar, the next 7 days, the claim report) lives on
            the Weather page; say where it is instead of leaving it to be found. */}
        {onWeatherPage && <button onClick={onWeatherPage} title="Radar, the next 7 days and the season by county" style={{ ...sel, cursor:"pointer", color:"#1f5fb0", fontWeight:700, padding:"5px 10px", minHeight: isMobile ? 40 : undefined }}>Weather page</button>}
        {/* ~40px tall on a phone: these are the page's main control and were 26px. */}
        <div role="tablist" style={{ marginLeft:"auto", display:"flex", gap:3, background:T.surfaceAlt, border:`1px solid ${T.line}`, borderRadius:12, padding:4, flexWrap:"wrap" }}>
          {[[7,"7 days"],[14,"14 days"],[30,"30 days"],[0,`${yr} season`],["plant","Since plant"]].map(([d,l]) => {
            const off = d === 0 && !seasonStarted;
            return (
            <button key={String(d)} onClick={()=>!off && setDays(d)} disabled={off}
              title={off ? `The ${yr} season starts April 1` : d==="plant" ? "Rain & GDD from each field's own planting date" : d===0 ? `April 1 to ${season.open ? "yesterday" : "November 30"}` : undefined}
              role="tab" aria-selected={days===d}
              style={{ border:"none", borderRadius:9, padding: isMobile ? "0 12px" : "6px 12px", minHeight: isMobile ? 40 : 32, fontSize: isMobile ? 13 : 12.5, fontWeight:700, cursor: off ? "default" : "pointer", fontFamily:"inherit",
              background: days===d ? T.surface : "transparent", color: off ? "#b8c4b8" : days===d ? T.ink : T.faint, boxShadow: days===d ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>{l}</button>
            );
          })}
        </div>
      </div>

      {/* A read that failed says so. It used to be swallowed, and the table then
          showed blanks that looked like fields with no rain. */}
      {err && <div style={{ ...card, marginBottom:12, color:"#8a4a1a", background:"#fdf5e6", borderColor:"#e8d3a8", fontSize:12.5 }}>
        {portal ? "Some weather couldn’t be read just now, so blanks below may fill in on a refresh." : `Part of the weather read failed (${err}). Blanks below may be that, not missing data.`}
      </div>}
      {!stats ? (
        <div style={{ ...card, color:"#60735f" }}>Reading weather for {fields.length} field{fields.length!==1?"s":""}…</div>
      ) : days === 0 && !seasonStarted ? (
        <div style={{ ...card, color:"#60735f", lineHeight:1.5 }}>The {yr} season starts April 1. Use 7, 14 or 30 days, or Since plant for fall-seeded fields.</div>
      ) : !withData.length ? (
        <div style={{ ...card, color:"#60735f", lineHeight:1.5 }}>
          {portal
            ? <>Rain data for these fields isn’t in yet. It fills in nightly.</>
            : <>No 1&nbsp;km rainfall on file for these fields in this window yet.{err ? ` (${err})` : ""} It fills in nightly, and the full history loads from the <b>Per-Field Rainfall Backfill</b> on the Sync Log.</>}
        </div>
      ) : (
        <>
          {/* The hero, in the Weather page's look: the farm's average rain for the window,
              the area's percent of normal as the pill (gridMET over its own normal, the
              same days), then GDD and what is still filling in. */}
          <WxHero isMobile={isMobile}
            eyebrow={`${farmName ? farmName + " · " : ""}${plantMode ? "Rain · since plant" : `Rain · ${days === 0 ? "season" : `${days} days`} through ${fmMd(range.to)}`}`}
            value={avgIn != null ? avgIn.toFixed(2) : "—"} unit={avgIn != null ? `in, average of ${withData.length - rainPartial} field${withData.length - rainPartial === 1 ? "" : "s"}` : null}
            pill={avgRainPct != null ? `area ${Math.round(avgRainPct)}% of normal` : null} pillTone={wxvPctTone(avgRainPct)}
            line={avgRainNormIn != null ? `Normal for the same days ${avgRainNormIn.toFixed(2)} in, from the grid cell under each field.` : null}
            stats={[
              avgGdd != null ? { v: `${Math.round(avgGdd).toLocaleString()} GDD`, l: avgGddNorm != null ? `${plantMode ? "since plant" : "season"}, ${Math.round(pctOf(avgGdd, avgGddNorm))}% of normal ${Math.round(avgGddNorm).toLocaleString()}` : `${plantMode ? "since plant" : "season"} average` } : null,
              { v: String(withData.length), l: `field${withData.length === 1 ? "" : "s"} with rain on file` },
              rainPartial > 0 ? { v: String(rainPartial), l: portal ? "still filling in, left out of the average" : "awaiting rainfall backfill, left out", tone: WXV_TONE.warn[0] } : null,
              gddPartial > 0 ? { v: String(gddPartial), l: "short on temperature readings, left out of GDD", tone: WXV_TONE.warn[0] } : null,
            ]} />
          {/* On a phone the five-column table clipped past the screen edge; each field is
              one row instead, its name and crop on the left and the rain on the right.
              Tapping it opens the field on the map. */}
          {isMobile ? (
            <div style={{ ...card, padding:"0 14px" }}>
              {display.map((f, i) => {
                const inches = f.mm != null ? f.mm / 25.4 : null;
                const p = f.rainOk ? f.rainPct : null;
                const gp = pctOf(f.gdd, f.gddNorm);
                return (
                  <button key={f.key+i} disabled={!onLocate}
                    onClick={()=>onLocate && onLocate({ farmer_id:f.r.farmer_id, clu_id:f.r.clu_id, lat:f.r.latitude!=null?Number(f.r.latitude):null, lon:f.r.longitude!=null?Number(f.r.longitude):null })}
                    style={{ display:"flex", alignItems:"center", gap:12, width:"100%", textAlign:"left", minHeight:58, padding:"9px 0", border:"none",
                             borderTop: i ? "1px solid #eef3ee" : "none", background:"none", cursor:onLocate?"pointer":"default", fontFamily:"inherit", color:T.ink }}>
                    <span style={{ flex:1, minWidth:0 }}>
                      <span style={{ display:"block", fontSize:15, fontWeight:800, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{nameFor(f)}</span>
                      <span style={{ display:"block", fontSize:12, color:"#4f6a4c", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {[f.r.crop ? fmCropName(f.r.crop, f.r.type_code) : null, cty(f.r) ? fmTitleCase(cty(f.r)) : null, f.gdd!=null ? `${Math.round(f.gdd).toLocaleString()} GDD${gp!=null?` (${Math.round(gp)}%)`:""}` : null].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span style={{ textAlign:"right", flexShrink:0 }}>
                      <span style={{ ...uiNum, display:"block", fontSize:16, fontWeight:800, color: inches==null?"#9aa89a":"#1f5fb0" }}>{inches!=null?`${inches.toFixed(2)}"`:"—"}</span>
                      <span style={{ display:"block", fontSize:11, fontWeight:700, color: p!=null ? rainPctColor(p) : "#9aa89a" }}>
                        {inches!=null && !f.rainOk ? "partial" : p!=null ? `area ${Math.round(p)}% of normal` : "rain"}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
          <div style={{ ...card, padding:0, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5, minWidth:580 }}>
                <thead><tr style={{ color:"#60735f", fontSize:10.5, textTransform:"uppercase", letterSpacing:"0.04em", background:"#f6faf6" }}>
                  {th("Field","field")}{th("Crop","crop")}{th("County","county")}{th(`Rain (${plantMode?"since plant":days===0?"season":days+"d"})`,"rain",true)}{th(plantMode?"GDD (since plant)":`${yr} season GDD`,"gdd",true)}
                </tr></thead>
                <tbody>
                  {display.map((f, i) => {
                    const inches = f.mm != null ? f.mm / 25.4 : null;
                    return (
                      <tr key={f.key+i} style={{ borderTop:"1px solid #f0f4f0" }}>
                        <td style={{ padding:"7px 12px", color:"#1f3320" }}>
                          {onLocate && <button onClick={()=>onLocate({ farmer_id:f.r.farmer_id, clu_id:f.r.clu_id, lat:f.r.latitude!=null?Number(f.r.latitude):null, lon:f.r.longitude!=null?Number(f.r.longitude):null })}
                            title="Show on map" aria-label="Show on map" style={{ border:"none", background:"none", cursor:"pointer", marginRight:5, padding:0 }}><FmPinIcon /></button>}
                          {editing === f.clu ? (
                            <span style={{ display:"inline-flex", gap:5, alignItems:"center" }}>
                              <input autoFocus value={draft} onChange={e=>setDraft(e.target.value)} placeholder={namedFromJd(f) ? `JD: ${jdNameOf(f).name}` : "e.g. Sacket west"}
                                onKeyDown={e=>{ if(e.key==="Enter") saveLabel(f); if(e.key==="Escape") setEditing(null); }}
                                style={{ padding:"3px 7px", borderRadius:6, border:"1px solid #cdd9cd", fontSize:12.5, width:150 }} />
                              <button onClick={()=>saveLabel(f)} style={{ border:"none", background:"none", cursor:"pointer", color:"#2d6a1a", fontWeight:800, fontFamily:"inherit", fontSize:12 }}>Save</button>
                              <button onClick={()=>setEditing(null)} style={{ border:"none", background:"none", cursor:"pointer", color:"#9a3030", fontFamily:"inherit", fontSize:12 }}>Cancel</button>
                            </span>
                          ) : (
                            <>
                              <b>{nameFor(f)}</b>
                              {/* A John Deere name is marked, because it is THEIR name for the
                                  field and not one of ours — and the FSA key stays beside any
                                  name at all, so the row still ties to an acreage report. */}
                              {namedFromJd(f) && <span title={jdNameOf(f).via === "ftf"
                                ? "Name from John Deere Operations Center — matched by where their combine ran on this field (no boundary overlap on file)"
                                : "Name from John Deere Operations Center — this field's centroid falls inside that JD boundary"}
                                style={{ display:"inline-block", width:7, height:7, borderRadius:4, background:T.green, marginLeft:6, verticalAlign:"middle", opacity: jdNameOf(f).via === "ftf" ? 0.5 : 0.85 }} />}
                              {(labels[f.clu] || namedFromJd(f)) && <span style={{ color:"#c2cdc2", fontSize:10 }}> ·&nbsp;{f.r.fsn||"—"}-{f.r.tract||"—"}-{f.r.field_id||"—"}</span>}
                              {canEdit && <button onClick={()=>{ setEditing(f.clu); setDraft(labels[f.clu]||""); }} title={namedFromJd(f) ? "Rename field (overrides the John Deere name)" : "Rename field"}
                                style={{ border:"none", background:"none", cursor:"pointer", fontSize:11, marginLeft:6, opacity:0.7, padding:0, color:"#2d6a1a", fontFamily:"inherit", fontWeight:700 }}>Rename</button>}
                              {plantMode && (() => { const pd = plantDateOf(f); return <span style={{ color:"#8aa088", fontSize:10, marginLeft:6 }}>{pd ? `planted ${fmMd(pd)}` : "no plant date"}</span>; })()}
                              {/* The field's own page: charts, daily detail, growth stage. */}
                              {onOpenField && <button onClick={()=>onOpenField(f.r, nameFor(f))} title="This field's daily weather, charts and growth stage"
                                style={{ border:"none", background:"none", cursor:"pointer", fontSize:11, marginLeft:8, padding:0, color:"#1f5fb0", fontFamily:"inherit", fontWeight:700 }}>Details</button>}
                            </>
                          )}
                        </td>
                        <td style={{ padding:"7px 12px", color:"#4a6e4a" }}>{f.r.crop ? (() => { const cc = cropChipColor(f.r.crop);
                          return <span style={{ display:"inline-block", fontSize:11.5, fontWeight:700, borderRadius:20, padding:"2px 9px", background:cc.bg, color:cc.fg, whiteSpace:"nowrap" }}>{fmCropName(f.r.crop, f.r.type_code)}</span>; })() : "—"}</td>
                        <td style={{ padding:"7px 12px", color:"#60735f" }}>{cty(f.r) ? fmTitleCase(cty(f.r)) : "—"}</td>
                        <td style={{ padding:"7px 12px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>
                          <div style={{ fontWeight:800, color: inches==null?"#bcc8bc":!f.rainOk?"#c8b48c":inches>=1?"#1f5fb0":inches>=0.25?"#4a6e4a":"#677167" }}>{inches!=null?`${inches.toFixed(2)}"`:"—"}</div>
                          {inches!=null && !f.rainOk
                            ? <div style={{ fontSize:10, fontWeight:700, color:"#b3701a" }} title="Per-field rainfall backfill incomplete for this field — % of normal suppressed until the full window is on file">partial · {f.rainDays}/{f.rainExp} days</div>
                            : (() => { const p = f.rainPct; return p!=null
                                ? <div style={{ fontSize:10, fontWeight:700, color:rainPctColor(p) }} title="The area's gridded rain against its 1995–2025 normal, over the same days as this field's radar readings">area {Math.round(p)}% of normal</div> : null; })()}
                        </td>
                        <td style={{ padding:"7px 12px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>
                          <div style={{ color: f.gdd==null?"#bcc8bc":!f.gddOk?"#c8b48c":"#1f3320" }}>{f.gdd!=null?Math.round(f.gdd).toLocaleString():(gddLoaded?"—":"…")}</div>
                          {f.gdd!=null && !f.gddOk
                            ? <div style={{ fontSize:10, fontWeight:700, color:"#b3701a" }} title="The grid cell under this field is missing readings in this window, so % of normal is held back">partial · {f.gddDays}/{f.gddExp} days</div>
                            : (() => { const p = pctOf(f.gdd, f.gddNorm); return p!=null
                              ? <div style={{ fontSize:10, fontWeight:700, color:gddPctColor(p) }}>{Math.round(p)}% of normal</div> : null; })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}
          <div style={{ marginTop:8 }}><WxNote>
            {portal
              ? <>Rain is measured by radar checked against rain gauges, at each field, so neighboring fields can differ. GDD are base 50°F with an 86° cap. <b>% of normal</b> compares the area around each field to its 1995–2025 average over the same days. A day with no reading is left out of both sides. </>
              : <>Rain = ~1&nbsp;km gauge-corrected MRMS radar at each field. GDD = base 50°F (86° cap) from the field's ~4&nbsp;km grid cell. <b>% of normal</b> = that cell's gridMET rain or GDD over its gridMET {WXF.NORMAL_LABEL}, on the same days; a day with no reading is left out of both. </>}
            {plantMode
              ? <><b>Since plant</b> adds up rain &amp; GDD from each field's own planting date (from the acreage report) through {fmMd(range.to)}. Fields with no reported planting date show “no plant date.”</>
              : <>Rain is {fmMd(range.from)} through {fmMd(range.to)}; {seasonStarted ? <>season GDD is {fmMd(seasonFrom)} through {fmMd(range.to)}.</> : <>season GDD starts April 1, {yr}.</>}</>}
          </WxNote></div>
        </>
      )}
    </div>
  );
}

// Crop → chip color, by keyword. Reused for the rotation-history cells.
function cropChipColor(crop) {
  const c = String(crop || "").toUpperCase();
  if (/SILAGE/.test(c))                     return { bg:"#e6f0e0", fg:"#3a6b2a" };
  if (/CORN/.test(c))                        return { bg:"#fbeccf", fg:"#8a5a12" };
  if (/SOY/.test(c))                         return { bg:"#e3f0da", fg:"#2f6b1f" };
  if (/WHEAT|TRITICALE|BARLEY|RYE|SPELT/.test(c)) return { bg:"#f3ebc8", fg:"#8a6d16" };
  if (/OAT/.test(c))                         return { bg:"#f0e7cf", fg:"#7a5f1a" };
  if (/HAY|ALFALFA|GRASS|FORAGE|PASTURE/.test(c)) return { bg:"#d9efe6", fg:"#1f6b56" };
  if (/BEAN|PEA|DRY/.test(c))                return { bg:"#eae4f2", fg:"#5a3f8a" };
  if (/PRF|RAINFALL/.test(c))                return { bg:"#e6e6f5", fg:"#4a4a8a" };
  return { bg:"#eef2ee", fg:"#4a6e4a" };
}

// FIELD CROP HISTORY — per-field rotation, scoped to one farm. Reads field_crop_history
// (authoritative insurance crop from the AIP feed, per CLU per year — 2026 seeded, 2025
// via the CY2025 backfill, +1 year each season). Pivots to one row per field × a column
// per year. Sortable + CSV export. Renders only when the view is a single grower.
// ── SOIL SAMPLES TAB ────────────────────────────────────────────────────────
// The whole chain for one farm: what's on file and what's due (tracker) → bring
// samples in (import) → what to apply (recommendations) → what the machine runs
// (prescriptions). Scoped to the selected farm, because sampling is tracked per
// grower. The MAP keeps the layer and the reading control; this tab is where the
// work happens, which is the split that keeps the map from getting busy.
function SoilSamplesView({ rows, labels = {}, farmerId, farmerName, isMobile, onLocate }) {
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState(false);
  const [intervalYrs, setIntervalYrs] = useState(3);
  const [onlyWork, setOnlyWork] = useState(true);   // default to the work list, not all 141
  const [section, setSection] = useState("tracker");

  useEffect(() => {
    if (!farmerId) { setEvents(null); return; }
    let alive = true; setBusy(true);
    sbFetch("soil_sample_events", {
      select: "id,farmer_id,clu_id,field_name,sampled_on,tested_on,lab,retailer,technique,grid_acres,depth_in,extraction,buffer_method,sample_count,acres,source_kind,crs_confirmed,centroid_lat,centroid_lon",
      filters: [`farmer_id=eq.${farmerId}`], order: "sampled_on.desc",
    }).then(r => { if (alive) setEvents(r || []); })
      .catch(() => { if (alive) setEvents([]); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [farmerId]);

  // One entry per CLU, from the same policy rows the other tabs read.
  const fields = useMemo(() => {
    const seen = new Map();
    for (const r of (rows || [])) {
      const clu = String(r.clu_id || "").toLowerCase();
      if (!clu) continue;
      const cur = seen.get(clu);
      const ac = r.acres != null ? Number(r.acres) : null;
      if (cur) { if (ac != null) cur.acres = (cur.acres || 0) + ac; continue; }
      seen.set(clu, {
        clu,
        name: labels[clu] || `${r.fsn || "—"}-${r.tract || "—"}-${r.field_id || "—"}`,
        named: !!labels[clu],
        acres: ac, crop: r.crop || null,
        lat: r.latitude, lon: r.longitude,
      });
    }
    return [...seen.values()];
  }, [rows, labels]);

  const tracker = useMemo(() => buildSoilTracker(fields, events, intervalYrs), [fields, events, intervalYrs]);
  const shown = onlyWork ? tracker.rows.filter(r => r.status !== "current") : tracker.rows;

  const card = { background: "#fff", border: "1px solid #e4ece4", borderRadius: 10, padding: "14px 16px" };
  const tabBtn = (k, label, count) => (
    <button key={k} onClick={() => setSection(k)}
      style={{ cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 800,
               padding: "6px 14px", borderRadius: 999, whiteSpace: "nowrap",
               border: `1px solid ${section === k ? "#2d4a2d" : "#d9e4d9"}`,
               background: section === k ? "#2d4a2d" : "#fff",
               color: section === k ? "#fff" : "#5c6f5c" }}>
      {label}{count != null && count > 0 ? ` · ${count}` : ""}
    </button>
  );

  if (!farmerId) return (
    <div style={{ ...card, fontSize: 13, color: "#5c6f5c" }}>
      Pick <b>one farm</b> above. Soil sampling is tracked per grower — a book-wide list
      would answer a question nobody asks standing in front of a soil probe.
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {tabBtn("tracker", "Sampling", tracker.never + tracker.overdue)}
        {tabBtn("import", "Import")}
        {tabBtn("recs", "Recommendations")}
        {tabBtn("rx", "Prescriptions")}
      </div>

      {section === "tracker" && (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 11 }}>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: "#1a2e1a" }}>{farmerName || "Farm"}</span>
            <span style={{ fontSize: 12, color: "#5c6f5c" }}>
              <b style={{ color: tracker.never ? "#a33a2a" : "#5c6f5c" }}>{tracker.never}</b> never sampled
              {tracker.acresNever > 0 && <span style={{ color: "#60735f" }}> ({Math.round(tracker.acresNever).toLocaleString()} ac)</span>}
              {" · "}<b style={{ color: tracker.overdue ? "#8a6318" : "#5c6f5c" }}>{tracker.overdue}</b> past {intervalYrs} yr
              {" · "}<b style={{ color: "#3f6b33" }}>{tracker.current}</b> current
            </span>
            <label style={{ fontSize: 11.5, color: "#60735f", display: "inline-flex", alignItems: "center", gap: 5 }}
              title="Cornell recommends sampling at least once in three years (Phosphorus Guidelines for Field Crops in New York, 2022, §5.1). Change it for a grower on a tighter rotation.">
              interval
              <input type="number" min="1" max="10" value={intervalYrs}
                onChange={(e) => setIntervalYrs(Math.max(1, Math.min(10, Number(e.target.value) || 3)))}
                style={{ width: 44, fontSize: 11.5, padding: "3px 5px", borderRadius: 5, border: "1px solid #d9e4d9", textAlign: "right" }} />
              yr
            </label>
            {/* Default to the work list. 141 rows where 140 say "never" is a census, not
                a plan — the fields that need nothing are the ones you don't act on. */}
            <label style={{ fontSize: 11.5, color: "#5c6f5c", display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
              <input type="checkbox" checked={onlyWork} onChange={(e) => setOnlyWork(e.target.checked)} style={{ cursor: "pointer" }} />
              needs attention only
            </label>
            <span style={{ fontSize: 11, color: "#60735f", marginLeft: "auto" }}>{busy ? "loading…" : `${shown.length} of ${tracker.rows.length} fields`}</span>
          </div>
          <div style={{ maxHeight: 460, overflowY: "auto", border: "1px solid #e4ece4", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f2f6f2" }}>
                  {["Field", "Acres", "Crop", "Last sampled", "Age", "How", "Lab", ""].map((h, i) => (
                    <th key={h + i} style={{ textAlign: i === 1 || i === 4 ? "right" : "left", padding: "7px 10px",
                                             fontWeight: 700, color: "#5c6f5c", fontSize: 10, letterSpacing: "0.05em",
                                             textTransform: "uppercase", borderBottom: "1px solid #e4ece4",
                                             whiteSpace: "nowrap", position: "sticky", top: 0, background: "#f2f6f2" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map(r => {
                  const tone = r.status === "never" ? "#a33a2a" : r.status === "overdue" ? "#8a6318" : "#3f6b33";
                  const mappable = r.events.find(e => e.centroid_lat != null);
                  return (
                    <tr key={r.clu}>
                      <td style={{ padding: "6px 10px", borderBottom: "1px solid #f0f4f0", color: "#1a2e1a", fontWeight: 600 }}>
                        <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 3, background: tone, marginRight: 7, verticalAlign: "middle" }} />
                        {r.name}
                        {!r.named && <span style={{ color: "#b6bfb6", fontWeight: 400 }} title="No farmer field name on file — this is the FSN-tract-field id"> ·&nbsp;unnamed</span>}
                      </td>
                      <td style={{ ...uiNum, padding: "6px 10px", borderBottom: "1px solid #f0f4f0", textAlign: "right", color: "#5c6f5c" }}>{r.acres != null ? r.acres.toFixed(1) : "—"}</td>
                      <td style={{ padding: "6px 10px", borderBottom: "1px solid #f0f4f0", color: "#5c6f5c" }}>{fmCropName(r.crop)}</td>
                      <td style={{ padding: "6px 10px", borderBottom: "1px solid #f0f4f0", color: r.last ? "#5c6f5c" : "#a33a2a", fontWeight: r.last ? 400 : 700 }}>{r.last ? r.last.sampled_on : "never"}</td>
                      <td style={{ ...uiNum, padding: "6px 10px", borderBottom: "1px solid #f0f4f0", textAlign: "right", color: tone, fontWeight: 700 }}>{r.ageYrs != null ? `${r.ageYrs.toFixed(1)}y` : "—"}</td>
                      <td style={{ padding: "6px 10px", borderBottom: "1px solid #f0f4f0", color: "#5c6f5c" }}>
                        {r.last ? (r.last.technique === "grid" ? `grid${r.last.grid_acres ? ` ${Number(r.last.grid_acres).toFixed(1)} ac` : ""}` : (r.last.technique || "—")) : "—"}
                      </td>
                      <td style={{ padding: "6px 10px", borderBottom: "1px solid #f0f4f0", color: "#60735f" }}>{r.last ? (r.last.lab || "—") : "—"}</td>
                      <td style={{ padding: "6px 10px", borderBottom: "1px solid #f0f4f0", whiteSpace: "nowrap" }}>
                        {mappable && onLocate && (
                          <button onClick={() => onLocate(r, mappable)} title="Show this sampling on the map"
                            style={{ cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 700, padding: "3px 9px",
                                     borderRadius: 6, border: "1px solid #cdd9cd", background: "#f6faf6", color: "#2d4a2d" }}>map</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!shown.length && (
                  <tr><td colSpan={8} style={{ padding: "14px 10px", color: "#60735f" }}>
                    {tracker.rows.length ? "Every field is inside the sampling interval." : "No mapped fields for this farm yet."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {tracker.orphans.length > 0 && (
            <div style={{ fontSize: 11.5, color: "#8a6318", marginTop: 9, lineHeight: 1.45 }}>
              {tracker.orphans.length} sampling{tracker.orphans.length === 1 ? "" : "s"} could not be tied to a mapped field
              ({tracker.orphans.map(o => `${String(o.sampled_on).slice(0, 4)} ${o.field_name || "unnamed"}`).join(", ")}).
              Counted here rather than dropped — still the grower's data.
            </div>
          )}
        </div>
      )}

      {section === "import" && (
        <div style={{ ...card, fontSize: 13, color: "#5c6f5c", lineHeight: 1.55 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "#1a2e1a", marginBottom: 8 }}>Import soil samples</div>
          <p style={{ margin: "0 0 10px" }}>
            Loading runs through <code>tools/load-soil-shapefile.js</code> today — a dry run by
            default, <code>--commit</code> to write. It is not wired to a button yet <b>on purpose</b>:
            the four guards that make a lab file safe to store run there, and an in-app
            uploader must call the same implementation rather than a second copy of it.
          </p>
          <ul style={{ margin: "0 0 10px", paddingLeft: "1.3em" }}>
            <li>a unit of <code>None</code> means <b>not tested</b> — the value is dropped, never stored as 0</li>
            <li>cations that reproduce CEC × base saturation are stored <b>derived</b>, with the unit corrected to ppm</li>
            <li>no <code>.prj</code> ⇒ the CRS is unconfirmed and flagged, not assumed</li>
            <li>a depth of <code>None</code> is NULL, because Cornell's lime table is depth-dependent</li>
          </ul>
          <p style={{ margin: 0 }}>
            Next step is moving those guards into <code>api/_soil.js</code> so the CLI and an
            in-app upload share one path — and so they run server-side, where a portal
            upload can't bypass them.
          </p>
        </div>
      )}

      {section === "recs" && (
        <div style={{ ...card, fontSize: 13, color: "#5c6f5c", lineHeight: 1.55 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "#1a2e1a", marginBottom: 8 }}>Recommendations</div>
          {(() => {
            const withExt = (events || []).filter(e => e.extraction);
            const without = (events || []).filter(e => !e.extraction);
            return (
              <>
                <p style={{ margin: "0 0 10px" }}>
                  Cornell's guidelines are calibrated on the <b>Morgan</b> extraction. A lab
                  reporting Mehlich-3 has to be converted first, and that conversion needs
                  soil pH, P, Ca <b>and aluminium</b> together, from a lab Cornell has a
                  published equation for.
                </p>
                {without.length > 0 && (
                  <div style={{ background: "#f5eddd", border: "1px solid #e0cda0", borderRadius: 8, padding: "10px 12px", color: "#6b4e12" }}>
                    <b>{without.length} sampling{without.length === 1 ? "" : "s"} cannot be converted yet</b> — the
                    extraction method is not recorded on {without.length === 1 ? "it" : "them"}
                    ({without.map(e => `${String(e.sampled_on).slice(0, 4)} ${e.field_name || "field"}`).join(", ")}).
                    That is the file's gap, not a bug: the shapefile export leaves the method blank,
                    while the lab's own PDF states it. Load the PDF, or confirm the method with the
                    lab, and the conversion unlocks.
                  </div>
                )}
                {withExt.length > 0 && (
                  <div style={{ marginTop: 10 }}>{withExt.length} sampling{withExt.length === 1 ? " is" : "s are"} ready to convert.</div>
                )}
                <p style={{ margin: "10px 0 0", color: "#60735f", fontSize: 12.5 }}>
                  The rate engine itself — sufficiency (Cornell) or build (Tri-State), manure
                  credits, multiplier, budget cap, and the NY P Index gate — is the next build.
                  Nothing here will emit a number it cannot source.
                </p>
              </>
            );
          })()}
        </div>
      )}

      {section === "rx" && (
        <div style={{ ...card, fontSize: 13, color: "#5c6f5c", lineHeight: 1.55 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "#1a2e1a", marginBottom: 8 }}>Prescriptions</div>
          <p style={{ margin: "0 0 10px" }}>
            A prescription is the end state of an approved recommendation at a chosen product
            and rate, saved as its own map layer and exportable as a zipped shapefile
            (<code>.shp/.shx/.dbf/.prj</code>, EPSG:4326, numeric rate column ≤ 10 characters)
            that John Deere, AgLeader, Trimble and Climate all read.
          </p>
          <p style={{ margin: 0, color: "#60735f", fontSize: 12.5 }}>
            Blocked on the recommendation engine above — there is nothing to prescribe until a
            rate can be sourced. The step that will need your sign-off when it lands is
            nutrient → product: Cornell returns lbs P₂O₅ and tons of 100% ENV lime, while the
            spreader runs MAP and ag lime at a real analysis.
          </p>
        </div>
      )}
    </div>
  );
}

function FieldHistoryPanel({ rows, nameOf, cty, isMobile, onLocate, onPickFarm = null, cropYear = null, portal = false }) {
  const [hist, setHist] = useState(null);   // clu(lower) -> { year -> {crop, acres} }
  const [labels, setLabels] = useState({});
  const [sortKey, setSortKey] = useState("field");
  const [sortDir, setSortDir] = useState("asc");
  const card = { background:"#fff", border:"1px solid #d9e4d9", borderRadius:10, padding:"12px 14px" };
  const sel  = { padding:"6px 10px", borderRadius:8, border:"1px solid #cdd9cd", background:"#fff", fontSize:13, color:"#1a2e1a", cursor:"pointer" };

  const farmIds = useMemo(() => [...new Set(rows.map(r => r.farmer_id).filter(Boolean))], [rows]);
  const singleFarm = farmIds.length === 1;
  const farmerId = singleFarm ? farmIds[0] : null;
  const farmName = singleFarm ? (nameOf(rows.find(r => r.farmer_id === farmerId)) || "this farm") : null;

  // current-year field identity (name / path / county) from the mapper rows
  const cur = useMemo(() => {
    const m = {};
    for (const r of rows) { const clu = (r.clu_id || "").toLowerCase(); if (clu && !m[clu]) m[clu] = r; }
    return m;
  }, [rows]);

  useEffect(() => {
    if (!farmerId) { setHist(null); return; }
    let alive = true; setHist(null);
    (async () => {
      const rowsH = await sbFetch("field_crop_history", { select:"clu_id,crop_year,crop,acres,pp_acres,county,fsn,tract,field_id", filters:[`farmer_id=eq.${farmerId}`], order:"crop_year.desc" });
      const m = {};
      for (const h of (rowsH || [])) {
        const clu = (h.clu_id || "").toLowerCase(); if (!clu) continue;
        const y = (m[clu] = m[clu] || {});
        // one crop per (clu, year): keep the larger-acre crop for the year cell
        if (!y[h.crop_year] || (Number(h.acres)||0) > (Number(y[h.crop_year].acres)||0)) y[h.crop_year] = { crop: h.crop, acres: h.acres, pp_acres: h.pp_acres, county: h.county, fsn: h.fsn, tract: h.tract, field_id: h.field_id };
      }
      if (alive) setHist(m);
    })().catch(() => alive && setHist({}));
    return () => { alive = false; };
  }, [farmerId]);

  useEffect(() => {
    if (!farmerId) { setLabels({}); return; }
    let alive = true;
    const clus = [...new Set(rows.map(r => (r.clu_id||"").toLowerCase()).filter(Boolean))];
    if (!clus.length) { setLabels({}); return; }
    (async () => {
      const acc = {};
      for (let i = 0; i < clus.length; i += 150) {
        const b = clus.slice(i, i + 150);
        const r = await sbFetch("field_labels", { select:"clu_id,label", filters:[`clu_id=in.(${b.join(",")})`] });
        for (const x of (r || [])) acc[(x.clu_id || "").toLowerCase()] = x.label;
      }
      if (alive) setLabels(acc);
    })().catch(() => {});
    return () => { alive = false; };
  }, [farmerId]); // eslint-disable-line

  // The loaded fields' year is shown LIVE from them (policy_fields), so it stays fresh
  // as acreage reports arrive — field_crop_history only backstops it + supplies the rest.
  //
  // ⚠ THAT IS THE CROP YEAR ON SCREEN, NOT THE CALENDAR YEAR. `rows` is whichever crop
  // year the year menu picked, and this overlaid them onto the calendar year's column:
  // pick 2025 and the 2026 column showed the 2025 crops while the real 2026 rotation
  // vanished. The caller passes the year it loaded.
  const thisYear = useMemo(() => Number(cropYear) || new Date().getUTCFullYear(), [cropYear]);
  const years = useMemo(() => {
    const s = new Set([thisYear]);
    if (hist) for (const clu in hist) for (const y in hist[clu]) s.add(Number(y));
    return [...s].sort((a, b) => b - a);
  }, [hist, thisYear]);

  const nameForClu = (clu, fallbackRow) => labels[clu] || (() => { const r = fallbackRow || cur[clu] || (hist && hist[clu] && Object.values(hist[clu])[0]) || {}; return `${r.fsn||"—"}-${r.tract||"—"}-${r.field_id||"—"}`; })();

  const fieldRows = useMemo(() => {
    if (!hist) return [];
    const clus = new Set([...Object.keys(cur), ...Object.keys(hist)]);
    const out = [];
    for (const clu of clus) {
      const r = cur[clu];
      // start from backfilled history, then overlay the LIVE current year from the field row
      const byYear = { ...(hist[clu] || {}) };
      if (r && r.crop) byYear[thisYear] = { crop: r.crop, acres: r.acres != null ? Number(r.acres) : null, pp_acres: r.pp_acres != null ? Number(r.pp_acres) : null };
      // most-recent year that actually HAS a cell (the 2026 overlay is empty for fields that
      // dropped out of the book — their county/acres must come from the prior year on file)
      const cell0 = years.map(y => byYear[y]).find(c => c) || null;
      out.push({
        clu, r,
        name: nameForClu(clu, r),
        county: (r ? cty(r) : null) || (cell0 && cell0.county) || "—",
        acres: r && r.acres != null ? Number(r.acres) : (cell0 && cell0.acres != null ? Number(cell0.acres) : null),
        byYear,
      });
    }
    const sv = (f) => { switch (sortKey) {
      case "county": return (f.county || "").toLowerCase();
      case "acres":  return f.acres ?? -1;
      case "field":  return (f.name || "").toLowerCase();
      default: { const y = sortKey.startsWith("y") ? Number(sortKey.slice(1)) : null; return y ? ((f.byYear[y] && f.byYear[y].crop) || "").toLowerCase() : (f.name||"").toLowerCase(); }
    } };
    const dir = sortDir === "asc" ? 1 : -1, isStr = sortKey !== "acres";
    return out.sort((a, b) => { const va = sv(a), vb = sv(b); return (isStr ? String(va).localeCompare(String(vb)) : (va - vb)) * dir; });
  }, [hist, cur, years, thisYear, sortKey, sortDir, labels]); // eslint-disable-line

  const toggleSort = (k) => { if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir(k === "acres" ? "desc" : "asc"); } };

  const exportCsv = () => {
    const hdr = ["Field", "FSN-Tract-Field", "County", "Acres", ...years.map(String)];
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const lines = [hdr.map(esc).join(",")];
    for (const f of fieldRows) {
      lines.push([f.name, `${f.r?.fsn||""}-${f.r?.tract||""}-${f.r?.field_id||""}`, f.county, f.acres != null ? f.acres : "",
        ...years.map(y => (f.byYear[y] && f.byYear[y].crop) || "")].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `${String(farmName || "farm").replace(/[^a-z0-9]+/gi, "_")}_crop_history.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (!farmIds.length) return <div style={{ ...card, color:"#60735f" }}>No insured fields in view.</div>;
  if (!singleFarm && onPickFarm) return <FmFarmChooser rows={rows} nameOf={nameOf} onPick={onPickFarm} what="Crop history" />;
  if (!singleFarm) return (
    <div style={{ ...card, color:"#54724f", lineHeight:1.6 }}>
      <div style={{ fontWeight:800, color:"#1a2e1a", marginBottom:4 }}>Crop history is shown one farm at a time.</div>
      Pick a farm — click a grower in the Acreage Summary tab, or search one above — to see that operation's per-field rotation history.
      <div style={{ fontSize:11.5, color:"#677167", marginTop:6 }}>{farmIds.length} farms in the current view.</div>
    </div>
  );
  if (!hist) return <div style={{ ...card, color:"#60735f" }}><UiSpinner />Reading crop history for {farmName}…</div>;
  if (!years.length) return (
    <div style={{ ...card, color:"#60735f", lineHeight:1.5 }}>
      No crop history on file for {farmName} yet.{portal ? " It fills in from your acreage reports." : <> CY2026 seeds from the current acreage report; run the <b>CY2025 Backfill</b> on the Sync Log (<code>&amp;fields=1</code>) to add 2025. Older years need CDL or FSA-578.</>}
    </div>
  );

  const th = (label, k, right) => (
    <th onClick={()=>toggleSort(k)} style={{ textAlign:right?"right":"left", padding:"8px 10px", cursor:"pointer", userSelect:"none", whiteSpace:"nowrap" }}>
      {label}{sortKey===k ? (sortDir==="asc"?" ▲":" ▼") : ""}
    </th>
  );

  return (
    <div>
      <div style={{ ...card, marginBottom:12, display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        {!isMobile && <div style={{ fontSize:13, fontWeight:800, color:"#1a2e1a" }}>Crop history by field</div>}
        {!isMobile && <span style={{ fontSize:12, fontWeight:700, color:"#2d4a2d" }}>· {farmName}</span>}
        <span style={{ fontSize:11.5, color:"#60735f" }}>{fieldRows.length} fields · {years.length} yr{years.length!==1?"s":""} on file</span>
        <button onClick={exportCsv} title="Export to CSV" style={{ ...sel, marginLeft:"auto", color:"#2d4a2d", fontWeight:700, padding:"5px 10px", minHeight: isMobile ? 40 : undefined }}>Export</button>
      </div>
      {/* On a phone each field is a row with its last five years under it, the same strip
          the field page shows, instead of a table that ran off the right edge. */}
      {isMobile ? (
        <div style={{ ...card, padding:"0 14px" }}>
          {fieldRows.map((f, i) => {
            const ys = years.slice().sort((a, b) => a - b).slice(-5);
            return (
              <button key={f.clu+i} disabled={!(onLocate && f.r)}
                onClick={()=>onLocate && f.r && onLocate({ farmer_id:f.r.farmer_id, clu_id:f.r.clu_id, lat:f.r.latitude!=null?Number(f.r.latitude):null, lon:f.r.longitude!=null?Number(f.r.longitude):null })}
                style={{ display:"block", width:"100%", textAlign:"left", padding:"10px 0", border:"none", borderTop: i ? "1px solid #eef3ee" : "none",
                         background:"none", cursor: onLocate && f.r ? "pointer" : "default", fontFamily:"inherit", color:T.ink }}>
                <span style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                  <span style={{ flex:1, minWidth:0, fontSize:15, fontWeight:800, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                  <span style={{ ...uiNum, fontSize:13, fontWeight:800, flexShrink:0 }}>{f.acres!=null ? `${f.acres.toFixed(1)} ac` : ""}</span>
                </span>
                {f.county && f.county !== "—" && <span style={{ display:"block", fontSize:12, color:"#4f6a4c" }}>{fmTitleCase(f.county)} County</span>}
                <span style={{ display:"grid", gridTemplateColumns:`repeat(${ys.length || 1}, minmax(0,1fr))`, gap:5, marginTop:7, textAlign:"center" }}>
                  {ys.map(y => {
                    const cell = f.byYear[y];
                    const ppAc = cell && cell.pp_acres != null ? Number(cell.pp_acres) : 0;
                    return (
                      <span key={y} style={{ borderRadius:8, padding:"4px 2px", minWidth:0, background: cell ? fmTint(fieldCropColor(cell.crop)) : "#f4f6f4" }}>
                        <span style={{ display:"block", fontSize:10.5, color:"#4f6a4c" }}>{y}</span>
                        <span style={{ display:"block", fontSize:12, fontWeight:800, color:"#1a2e1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {cell ? fmCropName(cell.crop).replace(/^Soybeans$/, "Soy") + (ppAc > 0 ? " · PP" : "") : "—"}
                        </span>
                      </span>
                    );
                  })}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
      <div style={{ ...card, padding:0, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5, minWidth:520 }}>
            <thead><tr style={{ color:"#60735f", fontSize:10.5, textTransform:"uppercase", letterSpacing:"0.04em", background:"#f6faf6" }}>
              {th("Field","field")}{th("County","county")}{th("Acres","acres",true)}{years.map(y => th(String(y), `y${y}`))}
            </tr></thead>
            <tbody>
              {fieldRows.map((f, i) => (
                <tr key={f.clu+i} style={{ borderTop:"1px solid #f0f4f0" }}>
                  <td style={{ padding:"7px 10px", color:"#1f3320" }}>
                    {onLocate && f.r && <button onClick={()=>onLocate({ farmer_id:f.r.farmer_id, clu_id:f.r.clu_id, lat:f.r.latitude!=null?Number(f.r.latitude):null, lon:f.r.longitude!=null?Number(f.r.longitude):null })}
                      title="Show on map" aria-label="Show on map" style={{ border:"none", background:"none", cursor:"pointer", marginRight:5, padding:0 }}><FmPinIcon /></button>}
                    <b>{f.name}</b>
                    {labels[f.clu] && f.r && <span style={{ color:"#c2cdc2", fontSize:10 }}> ·&nbsp;{f.r.fsn||"—"}-{f.r.tract||"—"}-{f.r.field_id||"—"}</span>}
                  </td>
                  <td style={{ padding:"7px 10px", color:"#60735f" }}>{f.county && f.county !== "—" ? fmTitleCase(f.county) : "—"}</td>
                  <td style={{ padding:"7px 10px", textAlign:"right", fontVariantNumeric:"tabular-nums", color:"#4a6e4a" }}>{f.acres!=null?f.acres.toFixed(1):"—"}</td>
                  {years.map(y => {
                    const cell = f.byYear[y];
                    if (!cell) return <td key={y} style={{ padding:"7px 10px", color:"#c2cdc2" }}>—</td>;
                    const col = cropChipColor(cell.crop);
                    // acres = field total (planted + PP); pp_acres = the PP subset.
                    // A bare "PP" badge on a mostly-planted field reads as if the whole
                    // field was prevent-plant — so show the PP acreage and flag whether
                    // it's the WHOLE field or just a partial slice.
                    const ppAc = cell.pp_acres != null ? Number(cell.pp_acres) : 0;
                    const totAc = cell.acres != null ? Number(cell.acres) : null;
                    const plantedAc = totAc != null ? Math.max(0, totAc - ppAc) : null;
                    const fullPP = ppAc > 0 && (plantedAc != null ? plantedAc < 0.5 : false);
                    return <td key={y} style={{ padding:"7px 10px", whiteSpace:"nowrap" }}>
                      <span style={{ fontSize:11.5, fontWeight:700, color:col.fg, background:col.bg, padding:"2px 8px", borderRadius:8 }}>{fmCropName(cell.crop)}</span>
                      {ppAc > 0 && <span title={fullPP
                          ? `Entire field prevent plant · ${ppAc.toFixed(1)} ac`
                          : `${ppAc.toFixed(1)} of ${totAc!=null?totAc.toFixed(1):"?"} ac prevent plant${plantedAc!=null?` · ${plantedAc.toFixed(1)} ac ${fmCropName(cell.crop).toLowerCase()} planted`:""}`}
                        style={{ fontSize:10.5, fontWeight:800, color: fullPP?"#8a2a2a":"#9a5a1a", background: fullPP?"#fbe3e3":"#fdf1df", padding:"1px 5px", borderRadius:6, marginLeft:4 }}>
                        {fullPP ? "PP (all)" : `PP ${ppAc.toFixed(ppAc<10?1:0)} ac`}
                      </span>}
                    </td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
      <div style={{ fontSize:10.5, color:"#677167", marginTop:8, lineHeight:1.5 }}>
        {portal
          ? <>Each year shows the crop on that year’s acreage report for your insurance (silage and grain kept apart). Blank = not on file for that year.</>
          : <>Crop per field per year = the <b>reported insurance crop</b> from the AIP acreage feed (silage vs grain and double-crop labels preserved). The year on the map fills from its acreage report; other years fill from the AIP backfill. Blank = not on file for that year.</>}
      </div>
    </div>
  );
}

// ── GEOSPATIAL IMPORT ───────────────────────────────────────────────────────
// Drop in a shapefile (.zip, or .shp+.dbf) or GeoJSON; we parse it in-browser
// (shpjs reprojects to WGS84 via the .prj) and DEDUP every polygon against the
// boundaries we already have — keyed on the FSA CLU GUID, case-insensitively.
// So downloading a grower's WHOLE policy-shape set only imports the handful of
// fields we're missing, never a duplicate. v1 persists field boundaries into
// field_geometries (the layer the map draws). Planting / harvest / spraying /
// soil layers parse + preview + dedup-check here too — so you can confirm what a
// file holds and which records are new — but persist in a later phase (we wire
// each once we've seen a real export, rather than guess its attribute schema).
const GEO_IMPORT_KINDS = [
  { key: "boundary", label: "Field Boundaries",        live: true,  hint: "CLU polygons → drawn on the Field Mapper" },
  { key: "planting", label: "Planting (as-planted)",   live: false, hint: "plant date · variety · population" },
  { key: "harvest",  label: "Harvest (as-harvested)",  live: false, hint: "yield · moisture" },
  { key: "spray",    label: "Spraying / Applications", live: false, hint: "product · rate · date" },
  // "soil" was here as a not-yet-live kind. Removed 2026-08-24 with the parking:
  // an inert row in an import picker is still a promise, and a promise for
  // something nobody is building reads as a feature that is merely broken.
];
// ── THE ALL CUSTOMERS CLAIM LENS ────────────────────────────────────────────
//
// Does this grower pass a has-a-claim / no-claim filter for one crop year?
//
// ⚠ THE ROWS USE cropYear, NOT crop_year. Both in-memory shapes are camelCase —
// approvedYields carries its own `cropYear` from the policy it came from, and a
// claim row is built as `cropYear: c.crop_year` at the map. The first cut of this
// filter read `crop_year` on both, so `Number(undefined)` was NaN, nothing ever
// matched the year, and BOTH chips returned zero growers. That is not a filter
// that was too strict; it is one that could never match.
//
// ⚠ IT LIVES HERE, NOT INSIDE THE MEMO, so it can be driven by a checker. Inline
// it was unreachable from any harness — which is exactly how it shipped.
//
// ⚠ AND A GROWER WITH NO COVERAGE THAT YEAR IS IN NEITHER BUCKET. They cannot
// have opened a claim, so counting them under "no claim" pads a call list with
// farms that are not in the book this year.

// ── THE SILAGE-APPRAISAL LENS ───────────────────────────────────────────────
//
// Kevin's question, in his own words: "who grows corn but doesn't have a silage
// appraisal claim opened?" The has/no-CLAIM chips answer a nearby question and
// the gap is large — measured CY2026, 199 growers carry corn, 58 have a silage
// appraisal, 141 do not, and 67 OF THOSE 141 CARRY SOME OTHER 2026 CLAIM, so
// the plain chip hides exactly the farms he was asking for. Brian Thompson is
// one of the 67.
//
// ⚠ A GROWER WHO DOES NOT CARRY THE CROP IS IN NEITHER BUCKET — the same rule
// bookClaimLens follows for coverage. Without it "no silage appraisal" is true
// of every dairy in the book and the chip lists 280 farms instead of 141.
// `g.crops` is the CARRIED set (acres or liability), which is the same test the
// crop chips beside it use, so the two compose honestly.
const _CLU_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const _cleanClu = (v) => (v == null ? "" : String(v).trim().replace(/[{}]/g, "").toLowerCase());
const _isClu = (v) => _CLU_RE.test(_cleanClu(v));
// FSN / tract / field — trim + strip a leading-zero pad so "0127"==="127".
const _normId  = (v) => String(v == null ? "" : v).trim().toUpperCase().replace(/^0+(?=\d)/, "");
// Policy number → bare significant digits ("MP-0835689" / "835689" → "835689").
const _normPol = (v) => String(v == null ? "" : v).replace(/\D/g, "").replace(/^0+/, "");

// Two shapefile dialects, unified by always resolving to the FSA CLU GUID (the
// key field_geometries + the map join on):
//   • NAU — the polygon carries the FSA CLU GUID in an attribute → match on it.
//   • R&H — NO GUID; carries (FARM_SERIA=FSN, TRACT, CLU_NUMBER=field#) + POLICY_NUM.
//     We match that tuple to the insured policy_field and store under ITS clu_id.
// geomSet: Set of lower(clu_id) we already have a boundary for.
// fieldByClu: Map lower(clu_id) → policy_field {farmer, fsn, tract, field_id, crop, acres}.
// fieldByFTF: Map "polDigits|fsn|tract|field" → policy_field (incl. clu_id) — R&H path.
// ftfByLoose: Map "fsn|tract|field" → [policy_fields] — fallback when no policy col.
// ─── Import field NAMES from a farmer's boundary file (JD Operations Center /
// Climate FieldView export) ──────────────────────────────────────────────────
// The farmer's polygons carry human field names ("104-40 Acre lot") but won't
// line up 1:1 with our FSA CLUs. We match by CONTAINMENT — a book field's CLU
// centroid falling inside an imported polygon inherits that polygon's name. This
// handles the common case (one operational field spans several CLUs → all get the
// same name) and validated at 97% on Triple H's real JD export. Confident,
// unambiguous matches auto-apply; everything else (nearest-only, multi-match, or
// would-overwrite-a-typed-name) goes to a review list. Writes field_labels
// (source='import'), never touching geometry or insurance data.
function _fniPolys(geom) {            // GeoJSON → array of polygons, each = array of rings
  if (!geom) return [];
  if (geom.type === "Polygon") return [geom.coordinates];
  if (geom.type === "MultiPolygon") return geom.coordinates;
  return [];
}
function _fniPtIn(lon, lat, rings) {  // even-odd ray cast across all rings (outer + holes)
  let inside = false;
  for (const ring of (rings || [])) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
      if (((yi > lat) !== (yj > lat)) && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) inside = !inside;
    }
  }
  return inside;
}
function _fniCentroid(polys) {        // rough centroid = mean of the first polygon's outer ring
  const ring = polys[0] && polys[0][0]; if (!ring || !ring.length) return null;
  let x = 0, y = 0; for (const p of ring) { x += p[0]; y += p[1]; } return [x / ring.length, y / ring.length];
}
function FieldNameImportModal({ onClose, onImported, allRows = [], labels = {}, isMobile }) {
  const [fileName, setFileName]   = useState("");
  const [rawFeatures, setRawFeatures] = useState(null);  // parsed GeoJSON polygon features
  const [cols, setCols]           = useState([]);
  const [nameCol, setNameCol]     = useState("");
  const [centroids, setCentroids] = useState(null);      // lower(clu) → [lat,lon]
  const [decisions, setDecisions] = useState({});        // clu → { accept, name }
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState(null);
  const [showAuto, setShowAuto]   = useState(false);

  // Book fields (restrict-scoped in preview), indexed by lower(clu).
  const bookByClu = useMemo(() => {
    const m = new Map();
    for (const r of allRows) {
      if (!r.clu_id) continue; const k = r.clu_id.toLowerCase();
      if (!m.has(k)) m.set(k, { clu: k, farmer_id: r.farmer_id, farmer: (r.farmers && r.farmers.name) || "",
        fsn: r.fsn, tract: r.tract, field_id: r.field_id, lat: r.latitude != null ? Number(r.latitude) : null, lon: r.longitude != null ? Number(r.longitude) : null });
    }
    return m;
  }, [allRows]);

  // All CLU centroids (case-safe, indexed by lower) — the geometry we match against.
  useEffect(() => {
    let alive = true;
    sbFetch("field_geometries", { select: "clu_id,centroid_lat,centroid_lon" })
      .then(r => { if (!alive) return; const m = {}; for (const x of (r || [])) if (x.clu_id && x.centroid_lat != null) m[x.clu_id.toLowerCase()] = [Number(x.centroid_lat), Number(x.centroid_lon)]; setCentroids(m); })
      .catch(() => { if (alive) setCentroids({}); });
    return () => { alive = false; };
  }, []);

  const onFile = async (file) => {
    if (!file) return;
    setErr(null); setFileName(file.name); setRawFeatures(null);
    try {
      let fc;
      if (/\.zip$/i.test(file.name)) {
        const shp = await loadShp();
        fc = await shp(await file.arrayBuffer());
        if (Array.isArray(fc)) fc = { type: "FeatureCollection", features: fc.flatMap(o => o.features || []) };
      } else {
        fc = JSON.parse(await file.text());
      }
      const features = (fc.features || []).filter(f => f.geometry && (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon"));
      if (!features.length) { setErr("No field polygons found in that file."); return; }
      const keys = [...new Set(features.flatMap(f => Object.keys(f.properties || {})))];
      const guess = keys.find(k => /^field_?name$/i.test(k)) || keys.find(k => /field.*name|name.*field/i.test(k))
        || keys.find(k => /^name$/i.test(k)) || keys.find(k => /field/i.test(k)) || keys[0] || "";
      setCols(keys); setNameCol(guess); setRawFeatures(features);
    } catch (e) { setErr("Could not read that file: " + (e.message || e)); }
  };

  // Named polygons (from the chosen column) with normalized geometry + raw geometry
  // (kept so the farmer's own boundaries can be stored + overlaid on the map).
  const parsed = useMemo(() => {
    if (!rawFeatures) return null;
    return rawFeatures.map(f => { const polys = _fniPolys(f.geometry); const c = _fniCentroid(polys);
        return { name: String((f.properties || {})[nameCol] ?? "").trim(), polys, geom: f.geometry, cLat: c ? c[1] : null, cLon: c ? c[0] : null }; })
      .filter(p => p.name && p.polys.length);
  }, [rawFeatures, nameCol]);

  // Match every book CLU to a name by centroid-containment.
  const result = useMemo(() => {
    if (!parsed || !centroids) return null;
    const auto = [], review = [];
    for (const [clu, fld] of bookByClu) {
      const c = centroids[clu] || (fld.lat != null && fld.lon != null ? [fld.lat, fld.lon] : null);
      if (!c) continue;                                  // no location on file → can't match
      const [lat, lon] = c;
      const hits = parsed.filter(pp => pp.polys.some(poly => _fniPtIn(lon, lat, poly)));
      const existing = labels[clu] || null;              // an already-set name (manual or prior import)
      if (hits.length === 1) {
        const name = hits[0].name;
        // A human-typed name (or a different existing name) is never silently
        // overwritten by an import — it routes to review even when confident.
        if (existing && existing !== name) review.push({ clu, fld, name, confidence: 1, reason: "conflict", existing });
        else auto.push({ clu, fld, name, confidence: 1 });
      } else if (hits.length > 1) {
        review.push({ clu, fld, name: hits[0].name, options: [...new Set(hits.map(h => h.name))], confidence: 0.5, reason: "ambiguous", existing });
      } else {
        let best = null, bd = Infinity;
        for (const pp of parsed) { const pc = _fniCentroid(pp.polys); if (!pc) continue; const d = haversineMi([lat, lon], [pc[1], pc[0]]); if (d < bd) { bd = d; best = pp; } }
        if (best && bd < 0.6) review.push({ clu, fld, name: best.name, confidence: Math.max(0.1, 1 - bd), reason: "nearest", miles: bd, existing });
      }
    }
    const named = new Set([...auto, ...review].map(r => r.name));
    const orphans = parsed.filter(pp => !named.has(pp.name)).map(pp => pp.name);
    return { auto, review, orphans, totalBook: bookByClu.size };
  }, [parsed, centroids, bookByClu, labels]);

  // Seed decisions when a match set is (re)computed: auto + non-conflict review
  // pre-checked; conflicts (would overwrite a typed name) default OFF.
  useEffect(() => {
    if (!result) return;
    const d = {};
    for (const r of result.auto) d[r.clu] = { accept: true, name: r.name };
    for (const r of result.review) d[r.clu] = { accept: r.reason !== "conflict", name: r.name };
    setDecisions(d);
  }, [result]);

  const setDec = (clu, patch) => setDecisions(m => ({ ...m, [clu]: { ...m[clu], ...patch } }));
  const acceptedCount = result ? [...result.auto, ...result.review].filter(r => decisions[r.clu]?.accept && (decisions[r.clu]?.name || "").trim()).length : 0;

  const apply = async () => {
    if (!result) return; setBusy(true); setErr(null);
    const batch = `${fileName}@${new Date().toISOString().slice(0, 16)}`;
    const toWrite = [];
    for (const r of [...result.auto, ...result.review]) {
      const d = decisions[r.clu]; if (!d || !d.accept) continue;
      const nm = (d.name || "").trim(); if (!nm) continue;
      toWrite.push({ clu_id: r.clu, farmer_id: r.fld.farmer_id || null, label: nm, source: "import",
        match_confidence: r.confidence != null ? Math.round(r.confidence * 100) / 100 : null, import_batch: batch,
        updated_by: getCurrentUserEmail(), updated_at: new Date().toISOString() });
    }
    // A JD/FieldView export is one grower's operation — infer that grower from the
    // matched fields so ALL its polygons (incl. uninsured land) can be stored + shown
    // as a map overlay of the farmer's own field definitions vs our CLUs.
    const fCount = {};
    for (const r of [...result.auto, ...result.review]) { const fid = r.fld.farmer_id; if (fid) fCount[fid] = (fCount[fid] || 0) + 1; }
    const domFarmer = Object.entries(fCount).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    const bounds = (domFarmer && parsed) ? parsed.map(p => ({ farmer_id: domFarmer, field_name: p.name, geometry: p.geom,
      centroid_lat: p.cLat, centroid_lon: p.cLon, source_file: fileName, import_batch: batch, updated_by: getCurrentUserEmail() })) : [];
    try {
      for (let i = 0; i < toWrite.length; i += 100) await sbUpsert("field_labels", toWrite.slice(i, i + 100), "clu_id");
      if (domFarmer && bounds.length) {
        await sbDelete("farmer_field_boundaries", `farmer_id=eq.${domFarmer}`);   // clean replace on re-import
        for (let i = 0; i < bounds.length; i += 100) await sbInsert("farmer_field_boundaries", bounds.slice(i, i + 100));
      }
      onImported && onImported(toWrite.length);
      onClose();
    } catch (e) { setErr("Save failed: " + (e.message || e)); setBusy(false); }
  };

  const overlay = { position: "fixed", inset: 0, zIndex: 3000, background: "rgba(20,30,20,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: isMobile ? "12px" : "34px 16px" };
  const card = { background: "#fff", borderRadius: 12, width: "100%", maxWidth: 760, boxShadow: "0 12px 40px rgba(0,0,0,.3)", overflow: "hidden" };
  const sel = { padding: "7px 10px", borderRadius: 8, border: "1px solid #cdd9cd", fontSize: 13, color: "#1a2e1a", background: "#fff" };
  const badge = (t, bg, c) => <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 5, background: bg, color: c, textTransform: "uppercase", letterSpacing: ".04em" }}>{t}</span>;
  const loading = rawFeatures && (!result);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ background: "#1a2e1a", color: "#fff", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>🏷 Import Farmer Boundaries</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#a8d878", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: isMobile ? "14px" : "18px 20px" }}>
          {!rawFeatures ? (
            <>
              <div style={{ fontSize: 13, color: "#4a6e4a", lineHeight: 1.6, marginBottom: 12 }}>
                Drop a farmer's boundary export (John Deere Operations Center / Climate FieldView) — a <b>.zip</b> shapefile or <b>.geojson</b>. We read the field names and match them to your insured fields by location; you confirm the uncertain ones. Nothing about coverage or geometry changes.
              </div>
              <label style={{ display: "block", border: "2px dashed #b7ccb7", borderRadius: 10, padding: "26px 16px", textAlign: "center", cursor: "pointer", background: "#f6faf6", color: "#4a6e4a", fontWeight: 700, fontSize: 13 }}>
                📎 Choose a .zip or .geojson boundary file
                <input type="file" accept=".zip,.geojson,.json" style={{ display: "none" }} onChange={e => onFile(e.target.files && e.target.files[0])} />
              </label>
              {err && <div style={{ marginTop: 12, fontSize: 12.5, color: "#b00", background: "#fdecea", padding: "9px 12px", borderRadius: 8 }}>{err}</div>}
            </>
          ) : loading ? (
            <div style={{ padding: 30, textAlign: "center", color: "#60735f" }}>Matching field names to your fields…</div>
          ) : (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 12.5, color: "#4a6e4a" }}>📎 <b>{fileName}</b> · {parsed.length} named fields</div>
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11.5, color: "#60735f" }}>Name column:</span>
                  <select value={nameCol} onChange={e => setNameCol(e.target.value)} style={sel}>
                    {cols.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
              </div>

              {/* summary */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ flex: "1 1 120px", background: "#eef7e9", border: "1px solid #cfe6c2", borderRadius: 9, padding: "10px 12px" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#2d6a1a" }}>{result.auto.length}</div>
                  <div style={{ fontSize: 11, color: "#4a6e4a", fontWeight: 700 }}>auto-match (confident)</div>
                </div>
                <div style={{ flex: "1 1 120px", background: "#fdf5e6", border: "1px solid #e8d3a8", borderRadius: 9, padding: "10px 12px" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#b06a1f" }}>{result.review.length}</div>
                  <div style={{ fontSize: 11, color: "#7a5a1a", fontWeight: 700 }}>need review</div>
                </div>
                <div style={{ flex: "1 1 120px", background: "#f2f4f2", border: "1px solid #dbe3db", borderRadius: 9, padding: "10px 12px" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#6b7a6b" }}>{result.orphans.length}</div>
                  <div style={{ fontSize: 11, color: "#6b7a6b", fontWeight: 700 }}>file fields not insured</div>
                </div>
              </div>

              {/* review list */}
              {result.review.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#7a5a1a", marginBottom: 6 }}>Review these {result.review.length}</div>
                  <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #eee3cf", borderRadius: 9 }}>
                    {result.review.map(r => {
                      const d = decisions[r.clu] || {};
                      return (
                        <div key={r.clu} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderBottom: "1px solid #f3ece0" }}>
                          <input type="checkbox" checked={!!d.accept} onChange={e => setDec(r.clu, { accept: e.target.checked })} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <input value={d.name || ""} onChange={e => setDec(r.clu, { name: e.target.value })} style={{ ...sel, width: "100%", boxSizing: "border-box", padding: "5px 8px" }} />
                            <div style={{ fontSize: 10.5, color: "#8a7a5a", marginTop: 3, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                              {r.reason === "conflict" && badge("would replace", "#fde0e0", "#b1442e")}
                              {r.reason === "ambiguous" && badge("in 2+ fields", "#fdf0d8", "#b06a1f")}
                              {r.reason === "nearest" && badge(`nearest ${r.miles.toFixed(2)} mi`, "#e6eef7", "#1f5fb0")}
                              <span>{r.fld.farmer} · {r.fld.fsn}-{r.fld.tract}-{r.fld.field_id}</span>
                              {r.existing && <span style={{ color: "#b1442e" }}>· now: “{r.existing}”</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* auto list (collapsed) */}
              {result.auto.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div onClick={() => setShowAuto(s => !s)} style={{ fontSize: 12, fontWeight: 800, color: "#2d6a1a", cursor: "pointer" }}>
                    {showAuto ? "▾" : "▸"} {result.auto.length} confident auto-matches
                  </div>
                  {showAuto && (
                    <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #d6ecca", borderRadius: 9, marginTop: 6 }}>
                      {result.auto.map(r => (
                        <div key={r.clu} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: "1px solid #edf6e7", fontSize: 12.5 }}>
                          <input type="checkbox" checked={!!(decisions[r.clu] || {}).accept} onChange={e => setDec(r.clu, { accept: e.target.checked })} />
                          <b style={{ color: "#1a2e1a" }}>{(decisions[r.clu] || {}).name || r.name}</b>
                          <span style={{ color: "#60735f", fontSize: 11 }}>{r.fld.farmer} · {r.fld.fsn}-{r.fld.tract}-{r.fld.field_id}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {err && <div style={{ marginBottom: 10, fontSize: 12.5, color: "#b00", background: "#fdecea", padding: "9px 12px", borderRadius: 8 }}>{err}</div>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#4a6e4a", marginRight: "auto" }}>Will name <b>{acceptedCount}</b> field{acceptedCount === 1 ? "" : "s"}.</span>
                <button onClick={onClose} style={{ ...sel, cursor: "pointer", fontWeight: 700 }}>Cancel</button>
                <button onClick={apply} disabled={busy || !acceptedCount} style={{ border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 800, fontSize: 13, cursor: busy || !acceptedCount ? "default" : "pointer", background: busy || !acceptedCount ? "#b7ccb7" : "#2d6a1a", color: "#fff" }}>
                  {busy ? "Saving…" : `Apply ${acceptedCount} name${acceptedCount === 1 ? "" : "s"}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GeoImportModal({ onClose, onImported, geomSet, fieldByClu, fieldByFTF, ftfByLoose, knownPolicies, isMobile }) {
  const [kind, setKind] = useState("boundary");
  const [parsing, setParsing] = useState(false);
  const [parseMsg, setParseMsg] = useState("");
  const [err, setErr] = useState(null);
  const [fileName, setFileName] = useState("");
  const [feats, setFeats] = useState(null);    // [{geometry, props}]
  const [propKeys, setPropKeys] = useState([]);
  const [strategy, setStrategy] = useState("clu");   // "clu" (NAU GUID) | "ftf" (R&H Farm/Tract/Field)
  const [cluCol, setCluCol] = useState("");
  const [acreCol, setAcreCol] = useState("");
  const [fsnCol, setFsnCol] = useState("");
  const [tractCol, setTractCol] = useState("");
  const [fieldCol, setFieldCol] = useState("");
  const [polCol, setPolCol] = useState("");
  const [sel, setSel] = useState(() => new Set());   // selected feature indices
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const kindLive = (GEO_IMPORT_KINDS.find(k => k.key === kind) || {}).live;

  const card = { background: "#fff", border: "1px solid #d9e4d9", borderRadius: 10, padding: "12px 14px" };
  const btn  = { padding: "8px 14px", borderRadius: 8, border: "1px solid #cdd9cd", background: "#fff", fontSize: 13, color: "#1a2e1a", cursor: "pointer", fontWeight: 700 };

  // Pick the attribute column most likely to hold the CLU GUID: among GUID-shaped
  // columns, the one whose values match the most CLUs we already know about (our
  // boundary set ∪ insured-field set); ties → a name containing "clu". This is
  // name-agnostic, so it works whether NAU/FSA call it CLU_ID, clu_identifier, etc.
  function detectCols(features, keys) {
    let bestClu = "", bestScore = -1;
    for (const k of keys) {
      let shaped = 0, known = 0;
      for (const f of features) {
        const v = f.props[k];
        if (!_isClu(v)) continue;
        shaped++;
        const c = _cleanClu(v);
        if (geomSet.has(c) || (fieldByClu && fieldByClu.has(c))) known++;
      }
      if (!shaped) continue;
      const nameBonus = /clu/i.test(k) ? 0.5 : 0;
      const score = known * 100 + shaped + nameBonus;     // known matches dominate
      if (score > bestScore) { bestScore = score; bestClu = k; }
    }
    // how many of bestClu's GUIDs we recognize → decides whether the GUID path is viable
    let cluKnown = 0;
    if (bestClu) for (const f of features) { const c = _cleanClu(f.props[bestClu]); if (geomSet.has(c) || (fieldByClu && fieldByClu.has(c))) cluKnown++; }

    const find = (re, opt = {}) => {
      let hit = "";
      for (const k of keys) {
        if (!re.test(k)) continue;
        if (opt.numeric && !features.some(f => { const n = Number(f.props[k]); return Number.isFinite(n) && n > 0; })) continue;
        if (opt.exclude && opt.exclude.test(k)) continue;
        hit = k; break;
      }
      return hit;
    };
    // R&H (and NAU's secondary) Farm/Tract/Field columns. field# = CLU_NUMBER on R&H —
    // exclude the GUID-ish RH_CLU_ID so "field" doesn't grab the wrong "clu" column.
    const fsnCol   = find(/farm_?seria|farm_?serial|^fsn|farm_?n(um|br)/i);
    const tractCol = find(/tract/i);
    const fieldCol = find(/clu_?num(ber)?|fsa_?field|^field|field_?id/i, { exclude: /clu_?id|rh_?clu/i });
    const polCol   = find(/policy_?n(um|br)|pol_?num|policynum/i);
    const acreCol  = find(/acre/i, { numeric: true });

    // Recommend strategy: GUID path if we actually recognize the GUIDs; else, if a
    // full Farm/Tract/Field set is present, the R&H tuple path; else GUID as fallback.
    const ftfReady = !!(fsnCol && tractCol && fieldCol);
    const strategy = (bestClu && cluKnown > 0) ? "clu" : (ftfReady ? "ftf" : (bestClu ? "clu" : "ftf"));
    return { cluCol: bestClu, acreCol, fsnCol, tractCol, fieldCol, polCol, strategy };
  }

  // Parse ONE file → [{geometry, props, _src}]. Tags each feature with its source
  // filename so a multi-file batch keeps provenance (and each polygon carries its
  // own policy#, so many farms in one drop resolve independently).
  async function parseOne(file) {
    const lower = file.name.toLowerCase();
    let collection = [];
    if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
      const txt = await file.text();
      const gj = JSON.parse(txt);
      const fc = gj.type === "FeatureCollection" ? gj
        : gj.type === "Feature" ? { features: [gj] }
        : gj.type ? { features: [{ type: "Feature", geometry: gj, properties: {} }] } : null;
      if (!fc) throw new Error(`${file.name}: unrecognized GeoJSON`);
      collection = fc.features || [];
    } else {
      const shp = await loadShp();
      const out = await shp(await file.arrayBuffer());
      const fcs = Array.isArray(out) ? out : [out];   // multi-layer zip → array
      collection = fcs.flatMap(fc => (fc && fc.features) || []);
    }
    return collection
      .filter(f => f && f.geometry && (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon" || f.geometry.type === "Point"))
      .map(f => ({ geometry: f.geometry, props: f.properties || {}, _src: file.name }));
  }

  async function parseFiles(fileList) {
    setErr(null); setResult(null); setFeats(null); setSel(new Set());
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setParsing(true);
    setFileName(files.length === 1 ? files[0].name : `${files.length} files`);
    try {
      const all = [];
      const errs = [];
      for (let i = 0; i < files.length; i++) {
        setParseMsg(`Parsing ${i + 1}/${files.length} — ${files[i].name}…`);
        try { const p = await parseOne(files[i]); if (!p.length) errs.push(`${files[i].name}: no polygons`); all.push(...p); }
        catch (e) { errs.push(e.message || String(e)); }
      }
      if (!all.length) throw new Error(errs.length ? errs.join("; ") : "No polygons found.");
      const keys = [...new Set(all.flatMap(f => Object.keys(f.props)))];
      const det = detectCols(all, keys);
      setFeats(all); setPropKeys(keys);
      setCluCol(det.cluCol); setAcreCol(det.acreCol);
      setFsnCol(det.fsnCol); setTractCol(det.tractCol); setFieldCol(det.fieldCol); setPolCol(det.polCol);
      setStrategy(det.strategy);
      if (errs.length) setErr(`Some files were skipped: ${errs.join("; ")}`);
    } catch (e) {
      setErr(e.message || String(e)); setFeats(null);
    } finally { setParsing(false); setParseMsg(""); }
  }

  // Classify every feature against what we already have — the dedup heart. Each
  // feature resolves to an FSA CLU GUID via the active strategy (a polygon carries
  // its OWN policy/CLU, so a bulk file spanning many farms just resolves each one
  // independently against the book-wide indices — multi-farm works by construction):
  //   clu (NAU): GUID straight from the attribute. Storable even if not insured yet.
  //   ftf (R&H): (policy#, FSN, tract, field#) → the insured policy_field's clu_id.
  //              No match ⇒ no GUID ⇒ can't store (a field we don't insure/track).
  const classified = useMemo(() => {
    if (!feats) return [];
    return feats.map((f, i) => {
      const ga = (f.geometry.type !== "Point") ? _geomCentroidAcres(f.geometry) : null;
      const acreAttr = acreCol ? Number(f.props[acreCol]) : NaN;
      const acres = Number.isFinite(acreAttr) && acreAttr > 0 ? acreAttr : (ga ? ga.acres : null);
      const base = { i, ga, acres };
      let clu = "", field = null;
      if (strategy === "ftf") {
        const fsn = fsnCol ? _normId(f.props[fsnCol]) : "", tr = tractCol ? _normId(f.props[tractCol]) : "", fl = fieldCol ? _normId(f.props[fieldCol]) : "";
        const pol = polCol ? _normPol(f.props[polCol]) : "";
        if (!fsn || !tr || !fl) return { ...base, clu: "", cat: "noclu", field: null, ftf: { pol, fsn, tr, fl } };
        if (polCol) field = fieldByFTF.get(`${pol}|${fsn}|${tr}|${fl}`) || null;
        // fallback (no policy col): take it only if every candidate resolves to the
        // SAME clu (e.g. one field insured under both corn + soy) — never guess across farms.
        if (!field) { const arr = ftfByLoose.get(`${fsn}|${tr}|${fl}`); if (arr && arr.length && new Set(arr.map(x => String(x.clu_id).toLowerCase())).size === 1) field = arr[0]; }
        if (!field) {
          // no keyed insured field. If the POLICY is in our book, this field's acres
          // just aren't reported yet → STAGE it (auto-activates when they are).
          // If the policy isn't ours at all → skip (nothing to ever attach to).
          const known = pol && knownPolicies && knownPolicies.has(pol);
          return { ...base, clu: "", cat: known ? "pending" : "nomatch", field: null, ftf: { pol, fsn, tr, fl } };
        }
        clu = String(field.clu_id || "").toLowerCase();
        if (!clu) return { ...base, clu: "", cat: "nomatch", field, ftf: { pol, fsn, tr, fl } };
        return { ...base, clu, cat: geomSet.has(clu) ? "dup" : "newins", field, ftf: { pol, fsn, tr, fl } };
      }
      // clu strategy
      const raw = cluCol ? f.props[cluCol] : null;
      clu = _cleanClu(raw);
      if (!clu || !_isClu(raw)) return { ...base, clu: "", cat: "noclu", field: null };
      if (geomSet.has(clu)) return { ...base, clu, cat: "dup", field: fieldByClu.get(clu) || null };
      field = fieldByClu.get(clu) || null;
      return { ...base, clu, cat: field ? "newins" : "newun", field };
    });
  }, [feats, strategy, cluCol, acreCol, fsnCol, tractCol, fieldCol, polCol, geomSet, fieldByClu, fieldByFTF, ftfByLoose, knownPolicies]);

  const counts = useMemo(() => {
    const c = { newins: 0, newun: 0, dup: 0, noclu: 0, nomatch: 0, pending: 0 };
    for (const r of classified) c[r.cat]++;
    return c;
  }, [classified]);

  // Default selection: new insured boundaries AND pending (import once, they light up
  // when acres land) — the full "download the grower's whole policy set" workflow.
  useEffect(() => {
    const next = new Set();
    for (const r of classified) if (r.cat === "newins" || r.cat === "pending") next.add(r.i);
    setSel(next);
  }, [classified]);

  const toggleRow = (i) => setSel(p => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const selectCat = (cat, on) => setSel(p => { const n = new Set(p); for (const r of classified) if (r.cat === cat) { on ? n.add(r.i) : n.delete(r.i); } return n; });

  const asMulti = (g) => g.type === "MultiPolygon" ? g.coordinates : g.type === "Polygon" ? [g.coordinates] : [];

  async function doImport() {
    if (!kindLive) return;
    const matched = classified.filter(r => sel.has(r.i) && (r.cat === "newins" || r.cat === "newun"));
    const staged  = classified.filter(r => sel.has(r.i) && r.cat === "pending");
    if (!matched.length && !staged.length) { setErr("Nothing selected to import."); return; }
    setImporting(true); setErr(null); setResult(null);
    try {
      // rows → field_geometries. Group by resolved CLU so a field split across
      // multiple polygons (same CLU) stores as ONE MultiPolygon, not a lost part.
      const byClu = new Map();
      for (const r of matched) {
        const parts = asMulti(feats[r.i].geometry);
        if (!parts.length) continue;
        const e = byClu.get(r.clu) || { coords: [], acres: 0, r };
        e.coords.push(...parts);
        e.acres += Number.isFinite(r.acres) ? r.acres : 0;
        byClu.set(r.clu, e);
      }
      const rows = [...byClu.entries()].map(([clu, { coords, acres, r }]) => {
        const geom = { type: "MultiPolygon", coordinates: coords };
        const ga = coords.length > 1 ? _geomCentroidAcres(geom) : r.ga;
        const fld = r.field || {};
        return { clu_id: clu, geometry: geom, centroid_lat: ga ? ga.lat : null, centroid_lon: ga ? ga.lon : null,
          acres: acres > 0 ? acres : (ga ? ga.acres : null), fsn: fld.fsn ?? null, tract: fld.tract ?? null, field_id: fld.field_id ?? null };
      });
      // pending → pending_boundaries (keyed by policy#/FSN/tract/field; no GUID yet).
      const pByKey = new Map();
      for (const r of staged) {
        const parts = asMulti(feats[r.i].geometry);
        if (!parts.length || !r.ftf) continue;
        const key = `${r.ftf.pol}|${r.ftf.fsn}|${r.ftf.tr}|${r.ftf.fl}`;
        const e = pByKey.get(key) || { coords: [], acres: 0, r };
        e.coords.push(...parts);
        e.acres += Number.isFinite(r.acres) ? r.acres : 0;
        pByKey.set(key, e);
      }
      const pending = [...pByKey.values()].map(({ coords, acres, r }) => {
        const geom = { type: "MultiPolygon", coordinates: coords };
        const ga = coords.length > 1 ? _geomCentroidAcres(geom) : r.ga;
        return { policy_number: polCol ? String(feats[r.i].props[polCol] ?? "") : "", fsn: r.ftf.fsn, tract: r.ftf.tr, field_id: r.ftf.fl,
          geometry: geom, centroid_lat: ga ? ga.lat : null, centroid_lon: ga ? ga.lon : null, acres: acres > 0 ? acres : (ga ? ga.acres : null) };
      });
      const token = localStorage.getItem("cloverag_access_token");
      if (!token) throw new Error("Not signed in — log out and back in, then retry.");
      const res = await fetch("/api/market?fn=geomupload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ rows, pending, source_file: fileName || "geomupload" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setResult(data);
      if ((data.inserted > 0 || data.promoted > 0) && onImported) onImported();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setImporting(false); }
  }

  const chip = (label, n, color, bg) => (
    <span style={{ fontSize: 12, fontWeight: 800, color, background: bg, padding: "4px 10px", borderRadius: 10 }}>{n} {label}</span>
  );
  const selCount = classified.filter(r => sel.has(r.i)).length;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(20,30,20,0.55)", zIndex: 4000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: isMobile ? 8 : 32, overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#f2f6f2", borderRadius: 14, padding: isMobile ? 14 : 22, width: "100%", maxWidth: 860, boxShadow: "0 12px 48px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 800, color: "#1a2e1a" }}>⬆ Import Geospatial Data</h2>
          <button onClick={onClose} style={{ ...btn, fontWeight: 800 }}>✕</button>
        </div>

        {/* data-type selector */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {GEO_IMPORT_KINDS.map(k => (
            <button key={k.key} onClick={() => { setKind(k.key); }} title={k.hint}
              style={{ ...btn, padding: "6px 12px", fontSize: 12.5,
                background: kind === k.key ? "#1a2e1a" : "#fff", color: kind === k.key ? "#a8d878" : (k.live ? "#1a2e1a" : "#677167"),
                borderColor: kind === k.key ? "#1a2e1a" : "#cdd9cd" }}>
              {k.label}{!k.live && " ·"}
            </button>
          ))}
        </div>
        {!kindLive && (
          <div style={{ ...card, background: "#fbf6ec", borderColor: "#e7d4a8", color: "#7a5a20", fontSize: 12.5, marginBottom: 12, lineHeight: 1.5 }}>
            <b>Preview only.</b> {GEO_IMPORT_KINDS.find(k => k.key === kind).label} files parse and dedup-check here so you can see what a layer contains, but saving lands in a later phase. Drop a real export and we’ll wire its attribute schema — no guessing.
          </div>
        )}

        {/* drop zone — accepts MANY files at once (drop a whole folder of R&H zips) */}
        <label onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files && e.dataTransfer.files.length) parseFiles(e.dataTransfer.files); }}
          style={{ ...card, display: "block", textAlign: "center", cursor: "pointer", padding: "22px 14px", marginBottom: 12,
            borderStyle: "dashed", borderColor: dragOver ? "#2d6a2d" : "#bcd0bc", background: dragOver ? "#eaf3ea" : "#fff" }}>
          <input type="file" accept=".zip,.shp,.geojson,.json" multiple style={{ display: "none" }}
            onChange={e => { if (e.target.files && e.target.files.length) parseFiles(e.target.files); }} />
          <div style={{ fontSize: 13, color: "#2d4a2d", fontWeight: 700 }}>{parsing ? (parseMsg || "Parsing…") : "Drop shapefiles (.zip) or GeoJSON here, or click to choose — multiple files OK"}</div>
          <div style={{ fontSize: 11.5, color: "#60735f", marginTop: 4 }}>
            {fileName ? `📎 ${fileName}` : "Select or drop a whole batch of R&H/NAU zips at once. A .zip with .shp + .dbf + .prj is ideal."}
          </div>
        </label>

        {err && <div style={{ ...card, color: "#b00", borderColor: "#e0bcbc", marginBottom: 12 }}>{err}</div>}

        {feats && (
          <>
            {/* strategy + column mapping */}
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 12.5, color: "#2d4a2d" }}><b>{feats.length}</b> feature{feats.length === 1 ? "" : "s"} parsed · match by:</div>
                <div style={{ display: "flex", gap: 4, background: "#eef3ee", borderRadius: 8, padding: 3 }}>
                  {[{ k: "clu", l: "CLU id (NAU)" }, { k: "ftf", l: "Farm/Tract/Field (R&H)" }].map(s => (
                    <button key={s.k} onClick={() => setStrategy(s.k)}
                      style={{ border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer",
                        background: strategy === s.k ? "#1a2e1a" : "transparent", color: strategy === s.k ? "#a8d878" : "#4a6e4a" }}>{s.l}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                {strategy === "clu" ? (
                  <label style={{ fontSize: 12, color: "#4a6e4a", display: "flex", alignItems: "center", gap: 6 }}>
                    CLU id:
                    <select value={cluCol} onChange={e => setCluCol(e.target.value)} style={{ ...btn, fontWeight: 600, cursor: "pointer" }}>
                      <option value="">(none)</option>
                      {propKeys.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </label>
                ) : (
                  <>
                    {[["FSN", fsnCol, setFsnCol], ["Tract", tractCol, setTractCol], ["Field #", fieldCol, setFieldCol], ["Policy # (opt)", polCol, setPolCol]].map(([lbl, val, setter]) => (
                      <label key={lbl} style={{ fontSize: 12, color: "#4a6e4a", display: "flex", alignItems: "center", gap: 5 }}>
                        {lbl}:
                        <select value={val} onChange={e => setter(e.target.value)} style={{ ...btn, fontWeight: 600, cursor: "pointer", padding: "6px 8px" }}>
                          <option value="">(none)</option>
                          {propKeys.map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </label>
                    ))}
                  </>
                )}
                <label style={{ fontSize: 12, color: "#4a6e4a", display: "flex", alignItems: "center", gap: 6 }}>
                  Acres:
                  <select value={acreCol} onChange={e => setAcreCol(e.target.value)} style={{ ...btn, fontWeight: 600, cursor: "pointer" }}>
                    <option value="">(compute)</option>
                    {propKeys.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
              </div>
            </div>

            {strategy === "clu" && !cluCol && (
              <div style={{ ...card, background: "#fbf6ec", borderColor: "#e7d4a8", color: "#7a5a20", fontSize: 12.5, marginBottom: 12 }}>
                No CLU id column detected. If this is a Rain &amp; Hail file it won’t have one — switch to <b>Farm/Tract/Field (R&amp;H)</b> above. Otherwise pick the GUID column.
              </div>
            )}
            {strategy === "ftf" && !(fsnCol && tractCol && fieldCol) && (
              <div style={{ ...card, background: "#fbf6ec", borderColor: "#e7d4a8", color: "#7a5a20", fontSize: 12.5, marginBottom: 12 }}>
                Need all three of FSN / Tract / Field # to match R&amp;H polygons to your insured fields. Pick the columns above.
              </div>
            )}
            {strategy === "ftf" && (fsnCol && tractCol && fieldCol) && !polCol && (
              <div style={{ ...card, background: "#eef3ee", borderColor: "#cdd9cd", color: "#54724f", fontSize: 12, marginBottom: 12 }}>
                No policy-number column set — matching by Farm/Tract/Field across the whole book. Pick a Policy # column to scope it (recommended when importing many farms); any ambiguous tuple is skipped, not guessed.
              </div>
            )}

            {/* dedup summary */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
              {chip("new · insured field", counts.newins, "#1f6f1f", "#e3f3e0")}
              {counts.pending > 0 && chip("waiting on acreage", counts.pending, "#1a5a8a", "#e2eef7")}
              {counts.newun > 0 && chip("new · no insured match", counts.newun, "#7a5a20", "#fbf2e0")}
              {chip("already on file", counts.dup, "#5a6a78", "#eef1f4")}
              {counts.nomatch > 0 && chip("not our policy (skipped)", counts.nomatch, "#8a4a2a", "#f7e6dc")}
              {counts.noclu > 0 && chip(strategy === "ftf" ? "missing key cols" : "no CLU id", counts.noclu, "#8a4a2a", "#f7e6dc")}
            </div>
            {counts.pending > 0 && (
              <div style={{ ...card, background: "#eef5fb", borderColor: "#c3d9ec", color: "#1a5a8a", fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
                <b>{counts.pending}</b> boundar{counts.pending === 1 ? "y is" : "ies are"} for policies you have but whose <b>acres aren’t keyed yet</b>. Importing parks them in storage — they <b>light up on the map automatically</b> the moment those acres get reported. No re-import needed.
              </div>
            )}

            {/* preview table */}
            <div style={{ ...card, padding: 0, maxHeight: 320, overflowY: "auto", marginBottom: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ position: "sticky", top: 0, background: "#eef3ee", color: "#54724f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  <th style={{ width: 28, padding: "6px 6px" }}></th>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>Status</th>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>Farmer / field</th>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>CLU</th>
                  <th style={{ textAlign: "right", padding: "6px 8px" }}>Acres</th>
                </tr></thead>
                <tbody>
                  {classified
                    .slice().sort((a, b) => ({ newins: 0, pending: 1, newun: 2, nomatch: 3, noclu: 4, dup: 5 }[a.cat] - { newins: 0, pending: 1, newun: 2, nomatch: 3, noclu: 4, dup: 5 }[b.cat]))
                    .map(r => {
                      const selectable = r.cat === "newins" || r.cat === "newun" || r.cat === "pending";
                      const tag = { newins: ["NEW", "#1f6f1f", "#e3f3e0"], pending: ["WAITING", "#1a5a8a", "#e2eef7"], newun: ["NEW?", "#7a5a20", "#fbf2e0"], dup: ["have", "#5a6a78", "#eef1f4"], nomatch: ["skip", "#8a4a2a", "#f7e6dc"], noclu: ["no key", "#8a4a2a", "#f7e6dc"] }[r.cat];
                      return (
                        <tr key={r.i} style={{ borderTop: "1px solid #f0f4f0", opacity: r.cat === "dup" ? 0.6 : 1 }}>
                          <td style={{ textAlign: "center", padding: "5px 6px" }}>
                            {selectable ? <input type="checkbox" checked={sel.has(r.i)} onChange={() => toggleRow(r.i)} /> : <span style={{ color: "#c0ccc0" }}>—</span>}
                          </td>
                          <td style={{ padding: "5px 8px" }}><span style={{ fontSize: 10.5, fontWeight: 800, color: tag[1], background: tag[2], padding: "2px 7px", borderRadius: 8 }}>{tag[0]}</span></td>
                          <td style={{ padding: "5px 8px", color: "#2d4a2d" }}>
                            {r.field ? <span><b>{r.field.farmer}</b> · {r.field.crop || ""} {r.field.fsn ? `· FSN ${r.field.fsn}/${r.field.field_id ?? ""}` : ""}</span>
                              : (strategy === "ftf" && (fsnCol || tractCol || fieldCol))
                                ? <span style={{ color: r.cat === "pending" ? "#3a6a8a" : "#677167" }}>{polCol && feats[r.i].props[polCol] ? `MP-${feats[r.i].props[polCol]} · ` : ""}FSN {feats[r.i].props[fsnCol] || "?"} / T {feats[r.i].props[tractCol] || "?"} / F {feats[r.i].props[fieldCol] || "?"}{r.cat === "pending" ? " · acres not reported yet — will activate" : r.cat === "nomatch" ? " · not insured" : ""}</span>
                                : <span style={{ color: "#677167" }}>{r.cat === "noclu" ? "—" : "no insured match"}</span>}
                          </td>
                          <td style={{ padding: "5px 8px", color: "#60735f", fontVariantNumeric: "tabular-nums", fontSize: 10.5 }}>{r.clu ? r.clu.slice(0, 8) + "…" : "—"}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", color: "#4a6e4a" }}>{r.acres != null ? r.acres.toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—"}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* quick select helpers */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12, fontSize: 12 }}>
              {counts.newun > 0 && (
                <button onClick={() => selectCat("newun", !classified.some(r => r.cat === "newun" && sel.has(r.i)))} style={{ ...btn, padding: "5px 10px", fontWeight: 600 }}>
                  Toggle the {counts.newun} no-match
                </button>
              )}
              <span style={{ marginLeft: "auto", color: "#54724f" }}><b>{selCount}</b> selected to import</span>
            </div>

            {result && (
              <div style={{ ...card, background: "#e9f4e6", borderColor: "#bcdcb4", color: "#1f6f1f", marginBottom: 12, fontSize: 13 }}>
                ✓ Imported <b>{result.inserted}</b> boundar{result.inserted === 1 ? "y" : "ies"}.
                {result.staged > 0 && <> Parked <b>{result.staged}</b> waiting on acreage.</>}
                {result.promoted > 0 && <> Activated <b>{result.promoted}</b> whose acres are already keyed.</>}
                {result.skippedExisting > 0 && <> Skipped <b>{result.skippedExisting}</b> already on file.</>}
                {result.dropped && result.dropped.length > 0 && <> Dropped {result.dropped.length} (bad geometry / no key).</>}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={onClose} style={btn}>Close</button>
              <button onClick={doImport} disabled={!kindLive || importing || selCount === 0}
                style={{ ...btn, background: (!kindLive || selCount === 0) ? "#bcd0bc" : "#1a2e1a", color: (!kindLive || selCount === 0) ? "#eef3ee" : "#a8d878", borderColor: "transparent", cursor: (!kindLive || selCount === 0) ? "not-allowed" : "pointer" }}>
                {importing ? "Importing…" : kindLive ? `Import ${selCount} boundar${selCount === 1 ? "y" : "ies"}` : "Saving not yet wired"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── ONE PAGE HEADER FOR EVERY FIELD MAPPER PAGE ON A PHONE ─────────────────────
// A page that works one farm at a time (Weather, History) used to stop at a paragraph
// telling you to go pick a farm somewhere else. Now the farms are the page: tap one.
function FmFarmChooser({ rows, nameOf, onPick, what }) {
  const farms = React.useMemo(() => {
    const by = new Map();
    for (const r of rows || []) {
      if (!r.farmer_id) continue;
      const e = by.get(r.farmer_id) || { id: r.farmer_id, name: nameOf(r) || "Farm", n: 0, ac: 0 };
      e.n += 1; e.ac += Number(r.acres) || 0;
      by.set(r.farmer_id, e);
    }
    return [...by.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [rows, nameOf]);
  return (
    <div style={{ background: "#fff", border: "1px solid #e0e8e0", borderRadius: 12, padding: "4px 16px 8px", fontFamily: T.sans, color: T.ink }}>
      <div style={{ fontSize: 13, color: "#4f6a4c", padding: "10px 0 6px" }}>{what} is shown one farm at a time. Pick a farm.</div>
      {farms.map((f) => (
        <button key={f.id} onClick={() => onPick(f.id)}
          style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", minHeight: 56, padding: "8px 0",
                   border: "none", borderTop: "1px solid #eef3ee", background: "none", cursor: "pointer", fontFamily: "inherit", color: T.ink }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 15, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
            <span style={{ display: "block", fontSize: 12, color: "#4f6a4c" }}>{f.n} field{f.n === 1 ? "" : "s"}{f.ac > 0 ? ` · ${Math.round(f.ac).toLocaleString()} ac` : ""}</span>
          </span>
          <span style={{ color: "#9aa89a", fontSize: 20, lineHeight: 1 }}>›</span>
        </button>
      ))}
    </div>
  );
}
// Kevin, 2026-10-31: "Make sure navigation makes sense, it's intuitive and not
// confusing to use." Every page off the map opens the same way: Back names where it
// goes, the title is in the serif the field page uses, and one line says whose fields
// and which year. Anything the page needs to change (farm, year) sits under it.
// A name a person typed wins, then the grower's own John Deere name, then the FSA key —
// the same order the field sheet's title uses, so the page a field opens is headed
// with the name the sheet just showed ("North 40", not "Field 1234-5678-3").
const fmFieldTitle = (f) => (f && f.label) ? f.label
  : (f && f.jdName) ? f.jdName
  : "Field " + [f && f.fsn, f && f.tract, f && (f.field_id != null ? f.field_id : f.field)].map((x) => (x == null || x === "" ? "—" : x)).join("-");
function FmPageHeader({ backLabel = "Field Mapper", onBack, title, sub = null, children = null, safeTop = false }) {
  // ⚠ STICK FLUSH TO THE PANE'S EDGE. A sticky `top:0` sticks below the scrolling
  // pane's top PADDING, not at its edge, so the page's own chips scrolled up through
  // that gap above the header (Kevin's phone, 2026-10-31: the year chips sat over
  // "Machine data"). Pull the stick point up by exactly that padding.
  const ref = useRef(null);
  const [stickTop, setStickTop] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let pane = el.parentElement;
    while (pane && pane !== document.body) {
      const oy = getComputedStyle(pane).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      pane = pane.parentElement;
    }
    if (!pane || pane === document.body) return;
    const pad = parseFloat(getComputedStyle(pane).paddingTop) || 0;
    setStickTop(pad > 0 ? -pad : 0);
  }, []);
  // ⚠ ONLY BACK AND THE TITLE ARE PINNED. The farm and year pickers used to ride in the
  // sticky band too, which on a phone made it a quarter of the screen tall, and the page's
  // own numbers scrolled up underneath it (Kevin, 2026-11-01: Kingsley's Season page, the
  // farm picker sitting over "fields · acres · harvested"; the same on Acreage over the
  // first rows). The controls now sit just under the band and scroll away with the page.
  return (<>
    <div ref={ref} style={{ position: "sticky", top: stickTop, zIndex: 60, background: "#fff", borderBottom: "1px solid #eef2ee",
                  padding: safeTop ? "calc(env(safe-area-inset-top,0px) + 4px) 16px 10px" : "4px 16px 10px", marginBottom: (safeTop || children) ? 0 : 12,
                  flexShrink: 0, fontFamily: T.sans, color: T.ink }}>
      {/* A REAL BUTTON, NOT A LINK. Kevin, 2026-10-31: "make sure it's very clear and
          easy to navigate back to field mapper landing page." A small green "‹ Map" read
          as a caption; this is a pill with a large tap target, named for where it goes. */}
      <button onClick={onBack} aria-label={`Back to ${backLabel}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 40, margin: "6px 0 8px", padding: "0 16px 0 12px",
                 border: "1px solid #cfe0cb", borderRadius: 20, background: "#eef6ec", fontFamily: "inherit", fontSize: 14.5, fontWeight: 800,
                 color: T.green, cursor: "pointer", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1, marginTop: -2 }}>‹</span>{backLabel}
      </button>
      <div style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 700, lineHeight: 1.15 }}>{title}</div>
      {sub && <div style={{ fontSize: 13, color: "#4f6a4c", marginTop: 2 }}>{sub}</div>}
    </div>
    {children && (
      <div style={{ position: "relative", zIndex: 55, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", background: "#fff",
                    padding: "10px 16px", marginBottom: safeTop ? 0 : 12, flexShrink: 0, fontFamily: T.sans, color: T.ink }}>{children}</div>
    )}
  </>);
}

// The App.jsx helpers the Season home reads, handed over as one object so there is a
// single definition of each (src/fieldmapper/FarmerSeasonHome.jsx).
const SEASON_UI = { PageHeader: FmPageHeader, cropName: fmCropName, unitLabel: jdYieldUnitLabel,
  isSilage: jdIsSilage, isCover: jdCropUninsured, cropColor: fieldCropColor };

// The phone Field Mapper takes the whole content pane, edge to edge, between the
// app's header and its bottom nav. The pane is the shell's scrolling column, and its
// padding is cancelled with negative margins rather than a fixed overlay: a fixed
// layer inside an iOS momentum-scrolling pane is exactly what broke the machine data
// view on Amos Smith's phone. The pane stops scrolling while the map is up, so a drag
// on the map moves the map and never the page.
function FmImmersive({ on = true, children }) {
  const ref = useRef(null);
  const [box, setBox] = useState(() => ({ mt: 0, ml: 0, mr: 0, mb: 0,
    h: typeof window !== "undefined" ? Math.max(360, window.innerHeight - 150) : 600 }));
  useEffect(() => {
    if (!on) return;
    const el = ref.current; if (!el) return;
    let pane = el.parentElement;
    while (pane && pane !== document.body) {
      const oy = getComputedStyle(pane).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      pane = pane.parentElement;
    }
    if (pane === document.body) pane = null;
    const prevOverflow = pane ? pane.style.overflowY : "";
    if (pane) { pane.scrollTop = 0; pane.style.overflowY = "hidden"; }
    let curMt = 0;
    const measure = () => {
      const node = ref.current; if (!node) return;
      if (pane) pane.scrollTop = 0;
      const cs = pane ? getComputedStyle(pane) : null;
      const pad = (k) => (cs ? parseFloat(cs[k]) || 0 : 0);
      const natural = node.getBoundingClientRect().top - curMt;
      const pr = pane ? pane.getBoundingClientRect() : null;
      const paneTop = pr ? pr.top + pane.clientTop : 0;
      const off = natural - paneTop;
      // Pull up over the pane's top padding only — never over content above it.
      const mt = off > 0 && off <= pad("paddingTop") + 1 ? -off : 0;
      curMt = mt;
      const bottom = pr ? pr.top + pane.clientTop + pane.clientHeight : window.innerHeight;
      const h = Math.max(320, Math.round(bottom - (natural + mt)));
      const next = { mt, ml: -pad("paddingLeft"), mr: -pad("paddingRight"), mb: -pad("paddingBottom"), h };
      setBox((b) => (b.mt === next.mt && b.ml === next.ml && b.mr === next.mr && b.mb === next.mb && b.h === next.h ? b : next));
    };
    measure();
    const t1 = setTimeout(measure, 60), t2 = setTimeout(measure, 400);
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    let ro = null;
    if (pane && typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(measure); ro.observe(pane); }
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      if (ro) ro.disconnect();
      if (pane) pane.style.overflowY = prevOverflow;
    };
  }, [on]);
  // Off, it is a plain wrapper. The element type at this spot never changes, so the
  // map inside is never torn down when the chop picker or a reload flips immersion.
  if (!on) return <div ref={ref}>{children(null)}</div>;
  return (
    <div ref={ref} style={{ position: "relative", marginTop: box.mt, marginLeft: box.ml, marginRight: box.mr, marginBottom: box.mb, height: box.h, overflow: "hidden" }}>
      {children(box.h)}
    </div>
  );
}

function FieldDataView({ farmers = [], prices = {}, onSelect, isMobile = false, initialFarmerId = null, mode = null, restrictFarmerIds = null,
                         initialFocus = null, onFocusConsumed = null, onNav = null }) {
  const isPortal = mode === "portal";   // portal farmers: view-only, own fields (RLS-scoped), no exports / agent nav
  // The agent's book view always lands on the whole boundary library (more fields
  // than the scoped loader handles), and it does not depend on the inventory read —
  // so start it NOW instead of after the 12 MB inventory finishes. Measured
  // 2026-10-16: the two ran back to back. Agent app only; a customer's fields are
  // loaded scoped, per CLU, exactly as before.
  useEffect(() => {
    if (!isPortal && !restrictFarmerIds && isCurrentUserAdmin()) loadFieldGeomsCached().catch(() => {});
  }, []);   // eslint-disable-line
  // Admin "view as" preview: the admin session bypasses RLS, so scope the rows to
  // the previewed user's farmers client-side. Null (normal) = no extra filtering.
  // MUST be memoized — a fresh Set/array each render cascades through rowsPhys →
  // filtered → the map's fc and rebuilds the whole Leaflet field layer every render
  // (closing popups mid-click, re-firing fitBounds — reads as the map "bouncing").
  // The caller passes a NEW array literal every render (preview: `[...previewIds]`),
  // so memoizing on the array *identity* never holds. Key on a stable string of the
  // sorted ids instead, so identical ids keep a stable Set across renders. This is
  // what stops the Weather/PRF overlay from flashing in "view as" preview mode.
  const _restrictKey = restrictFarmerIds ? restrictFarmerIds.slice().sort().join(",") : "";
  const _restrictSet = useMemo(() => _restrictKey ? new Set(_restrictKey.split(",")) : null, [_restrictKey]);
  // WHICH CROP YEAR'S FIELD MAP IS ON SCREEN.
  //
  // policy_fields held exactly one crop year, book-wide, from the day it was
  // created until the M13/NAU prior-year backfill landed 2026-08-13 — so this
  // screen never had a year concept and simply read the table. The moment a
  // second year appeared, an unscoped read drew every CLU once per year AND
  // filled the map with the prior year's un-mapped fields as boundary-less
  // points, which reads as "we lost the boundaries" when nothing was lost:
  // CY2025's field map is 9.0% un-mapped (1,267 fields) against CY2026's 1.9%
  // (280). The boundary library itself never changed.
  //
  // Defaults to CURRENT_CROP_YEAR — the year the book's PREMIUM is in, not the
  // calendar year (see adoptCropYearFromBook) — whenever the field map actually
  // has that year, and falls back to the newest year present so a rollover year
  // that hasn't been keyed yet renders the real current map instead of nothing.
  const [fieldYears, setFieldYears] = useState(null);  // crop years policy_fields HAS, newest first
  const [fieldYear, setFieldYear]   = useState(null);  // the one being drawn (null until discovered)
  // Off the current book? Every prior year is thinner than the live one — fewer
  // boundaries, fields since changed hands — so a patchier map is the DATA, not a
  // regression. Several things below have to know, not just the year control.
  const offCurrentYear = fieldYear != null && fieldYear !== CURRENT_CROP_YEAR;
  const [rows, setRows]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  // AFTER `err`, deliberately: `rows` stays null when the fetch below throws, so
  // without the error the hook files a failed load as "this farm has no fields".
  // Declared here rather than beside `rows` because reading `err` one line
  // earlier is a temporal dead zone — check-tdz.js catches that, but the reason
  // for the ordering belongs in the file.
  usePortalEmptyState("fields", (rows || []).length === 0, !loading, err);
  const [q, setQ]             = useState("");
  const [aipSet, setAipSet]       = useState(() => new Set());
  const [stateSet, setStateSet]   = useState(() => new Set());
  const [countySet, setCountySet] = useState(() => new Set());
  const [cropSet, setCropSet]     = useState(() => new Set());
  const [typeSet, setTypeSet]     = useState(() => new Set());  // corn Grain(GSG)/Silage(GSS)
  // Everyone lands on the map. Portal growers used to open on "My Acres" — the
  // reasoning was that they come here to check acres — but a page called Field
  // Mapper that opens on a table isn't the thing they came for, and My Acres is
  // one tab away. The map is the landing page on both sides now.
  // ── EXCEPT A FARMER, WHO LANDS ON THEIR SEASON (Kevin, 2026-10-31, mockup approved):
  // fields and acres by crop, yield and planting maps, and the season totals, with the map
  // one tap away. Every page's back button returns to that home (`fmHome`). Agents still
  // land on the map and open the same page for a farm from Season.
  const [view, setView]       = useState(isPortal ? "season" : "map");
  const fmHome = isPortal ? "season" : "map";
  const [seasonPage, setSeasonPage] = useState(null);      // Season home sub-page: null | {kind:"crop",key} | {kind:"totals"}
  const [weatherField, setWeatherField] = useState(null);  // field whose weather page is open (null = normal mapper)
  const [prfField, setPrfField] = useState(null);          // PRF field whose rainfall page is open
  const [mapFocus, setMapFocus] = useState(null);          // {farmer_id,clu_id,lat,lon} → jump the map to this field
  const mapViewportRef = useRef(null);                     // {center:[lat,lng],zoom} — last map viewport, to restore on Back
  const [selectedFarmerId, setSelectedFarmerId] = useState(initialFarmerId || null); // sticky farm for Weather/History
  const [expanded, setExpanded] = useState(() => new Set(initialFarmerId ? [initialFarmerId] : []));
  const [sortKey, setSortKey] = useState("county");
  const [sortDir, setSortDir] = useState("asc");
  const [groupSort, setGroupSort] = useState("name"); // 'name' | 'acres_desc' | 'acres_asc' — orders growers in both views

  const [cluAcres, setCluAcres] = useState({});   // lower(clu_id) → per-field acres (REP_ACRES from shapefile .dbf)
  const [geomClus, setGeomClus] = useState(null); // Set of lower(clu_id) that HAVE a boundary (null until loaded)
  const [centroids, setCentroids] = useState(null); // lower(clu_id) → [lon,lat]; feeds the physical-county classifier
  const [missingOpen, setMissingOpen] = useState(() => new Set());  // farmers expanded in the No-Boundary worklist (default collapsed)
  const [importOpen, setImportOpen] = useState(false);              // boundary/geo import modal
  const [nameImportOpen, setNameImportOpen] = useState(false);      // "Import field names" (boundaries-with-names) modal
  const [jdConnOpen, setJdConnOpen] = useState(false);             // "JD Connections" (Ops Center org list) overlay
  const [jdDataFarmer, setJdDataFarmer] = useState(null);          // farmer whose JD Field Data overlay is open (from the map)
  const [jdDataSet, setJdDataSet] = useState(null);               // Set of farmer_ids that HAVE JD field data
  // Which farms have machine data behind them. The portal needs this too now —
  // it decides whether the Machine Data tab opens on a farm's records or on the
  // connect panel — so it is narrowed by farm id rather than skipped. The handler
  // scopes a real farmer either way; the ids are what keeps "Preview as", which
  // runs on an ADMIN token, from answering book-wide.
  useEffect(() => {
    let alive = true;
    const jdIds = (restrictFarmerIds || []).filter(Boolean);
    if (isPortal && !jdIds.length) { setJdDataSet(new Set()); return; }
    const q = jdIds.length ? `&farmer=${jdIds.map(encodeURIComponent).join(",")}` : "";
    authedFetch(`/api/market?fn=jd-connected${q}`).then(r => r.json())
      .then(d => { if (alive) setJdDataSet(new Set(((d && d.connections) || []).map(c => c.farmer_id))); })
      .catch(() => { if (alive) setJdDataSet(new Set()); });
    return () => { alive = false; };
  }, [isPortal, restrictFarmerIds]); // eslint-disable-line

  // ── The grower's OWN field names, from John Deere Ops Center ────────────────
  // Resolved server-side by centroid-in-boundary (?fn=jd-field-names — see the
  // long note on hFieldNames in api/_jd.js for what it refuses and why), because
  // the jd_* tables are service-role only and a direct read returns nothing.
  //
  // Asked ONLY for the farms that actually have a JD connection (jdDataSet, at
  // most 18 today), never book-wide: resolving a farm that has no machine data
  // costs the same two queries and can only ever answer "nothing".
  const [jdNames, setJdNames] = useState({});     // lower(clu_id) → { name, jd_field_id, via }
  useEffect(() => {
    let alive = true;
    const ids = jdDataSet ? [...jdDataSet].filter(Boolean) : [];
    if (!ids.length) { setJdNames({}); return; }
    authedFetch(`/api/market?fn=jd-field-names&farmer=${ids.map(encodeURIComponent).join(",")}`)
      .then(r => r.json())
      .then(d => { if (alive) setJdNames((d && d.names) || {}); })
      .catch(() => { if (alive) setJdNames({}); });
    return () => { alive = false; };
  }, [jdDataSet]);

  const [labels, setLabels] = useState({});       // lower(clu_id) → custom field name (from field_labels)
  const [labelNonce, setLabelNonce] = useState(0);// bump to re-pull labels after an edit/import
  // `guar` (farmer_id → { byCrop:{ crop:{ liab,bu, counties:{ county:{liab,bu} } } } })
  // is derived below — a useMemo over the fetched rows, not state. See the note there.

  // Custom field names — one label per physical field (keyed on lower(clu_id), so it
  // rides across crop years and covers split-planted CLUs). Read is open to all authed
  // users; writes are RLS-scoped (agents = is_admin, farmers = their own CLUs). The
  // table is tiny, so pull it whole and index by clu.
  useEffect(() => {
    let alive = true;
    sbFetch("field_labels", { select: "clu_id,label" })
      .then(rows => { if (!alive) return; const m = {}; for (const r of (rows||[])) if (r.clu_id && r.label) m[r.clu_id.toLowerCase()] = r.label; setLabels(m); })
      .catch(() => { if (alive) setLabels({}); });
    return () => { alive = false; };
  }, [labelNonce]);

  // ── Per-CLU machine yield, for the map's Yield layer ────────────────────────
  // jd_field_ftf is the John Deere harvest already split onto OUR CLU ids — about
  // 1,290 yield rows over ~650 CLUs, so it's cheap to pull whole. Deliberately NOT
  // jd_yield_grids: that's 173 MB across 2,195 rows and would recreate exactly the
  // MI Yield problem (a 56k-row mount fetch) on a page you open constantly.
  //
  // Keeps the most RECENT harvest per CLU. Yields are compared within their own
  // crop downstream — corn averages 195 bu/ac and soybeans 53, so one shared scale
  // would paint every soybean field as "poor".
  const [cluYieldRows, setCluYieldRows] = useState(null);   // the jd-cluyield rows, as served
  useEffect(() => {
    let alive = true;
    // Server-side: jd_field_ftf is RLS-on with NO policies (JD data is
    // service-role only), so the direct sbFetch this replaces always came back
    // empty — no error, no rows — and the Yield layer silently drew nothing.
    authedFetch("/api/market?fn=jd-cluyield")
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        setCluYieldRows((d && d.rows) || []);
      })
      .catch(() => { if (alive) setCluYieldRows([]); });
    return () => { alive = false; };
  }, []);
  // lower(clu_id) → the field's size for the sliver test: the shapefile's own acres when
  // we have them (`cluAcres`), else the most acres any insured line puts on it.
  const cluSizeAc = useMemo(() => {
    const m = {};
    for (const r of (rows || [])) {
      const k = String(r.clu_id || "").toLowerCase(), a = Number(r.acres) || 0;
      if (k && a > (m[k] || 0)) m[k] = a;
    }
    for (const [k, a] of Object.entries(cluAcres || {})) if (Number.isFinite(a) && a > 0) m[k] = a;
    return m;
  }, [rows, cluAcres]);
  // ⚠ A NEIGHBOUR'S PASS IS NOT THIS FIELD'S YIELD (Kevin, 2026-10-31, on a 9.3 ac PRF
  // pasture showing "Combine yield 70.8 bu/ac": "it was not combined"). The soybean
  // field next door clipped 0.02 ac of its edge, and the page showed any pass that
  // touched the field. 4,690 of 27,186 yield rows were slivers under half an acre. A
  // pass counts for a field only when it covered at least 20% of it (and half an acre).
  // The accounted-for layer keeps reading every pass (`cluYieldAll`), unchanged, until
  // claim evidence is looked at on its own.
  const cluSliver = (r, k) => {
    if (r.acres == null) return false;   // older payload with no acres: drawn as before
    const a = Number(r.acres) || 0, f = cluSizeAc[k] || 0;
    return a < 0.5 || (f > 0 && a < 0.2 * f);
  };
  const buildCluYield = (rowsIn, dropSlivers) => {
    if (!rowsIn) return null;
    const m = {};
    for (const r of rowsIn) {
      const k = String(r.clu_id || "").toLowerCase();
      const v = Number(r.mean_value);
      if (!k || !isFinite(v) || v <= 0) continue;
      if (dropSlivers && cluSliver(r, k)) continue;
      // Plausibility guard, UNIT-AWARE. The old version dropped corn over 350
      // bu/ac to hide silage stored as grain; those rows are now corrected to
      // CORN SILAGE in ton/ac (2026-07-30), so the guard's job changed. What it
      // still catches is the OTHER artifact: operations recorded with
      // area_acres = 0, where yield-per-acre is a divide-by-zero blowup rather
      // than a unit error (Triple H 2023 "206-Silo Lot South" reads 6,927 bu/ac
      // at a perfectly normal 22% grain moisture). Real acres are unknowable, so
      // the value is excluded rather than rewritten.
      if (!jdYieldPlausible(r.crop, v)) continue;
      const prev = m[k];
      const cur = { crop: (r.crop || "").toUpperCase(), value: v, date: r.operation_date || null,
        unit: r.yield_unit || null, dm: r.mean_dm != null && isFinite(Number(r.mean_dm)) ? Number(r.mean_dm) : null };
      // The MAP draws the most recent pass whatever its source (unchanged). `ev` is the
      // most recent pass that may count as EVIDENCE — not gone or duplicate
      // (src/claims/jdEvidence.js) — and it is what the accounted-for layer reads.
      const evPrev = prev ? prev.ev : null;
      const ev = isJdEvidenceRow(r) && (!evPrev || String(cur.date || "") > String(evPrev.date || "")) ? cur : evPrev;
      if (!prev || String(r.operation_date || "") > String(prev.date || "")) {
        m[k] = { ...cur, ev };
      } else {
        m[k] = { ...prev, ev };
      }
    }
    return m;
  };
  const cluYieldAll = useMemo(() => buildCluYield(cluYieldRows, false), [cluYieldRows]); // eslint-disable-line
  const cluYield = useMemo(() => {
    const m = buildCluYield(cluYieldRows, true);
    // Evidence is read from every pass, so the shown entry carries the unfiltered `ev`.
    if (m && cluYieldAll) for (const k of Object.keys(m)) m[k] = { ...m[k], ev: cluYieldAll[k] ? cluYieldAll[k].ev : m[k].ev };
    return m;
  }, [cluYieldRows, cluYieldAll, cluSizeAc]); // eslint-disable-line

  // ── WHICH INSURED FIELDS ARE ACCOUNTED FOR ─────────────────────────────────
  // Kevin, 2026-10-01: *"I almost think some of this data should be incorporated in field
  // mapper some how, in addition to claims manager."* The map is where "which fields are
  // missing" reads fastest, because a hole in a block of colour is visible and a missing
  // row in a 108-row table is not.
  const [fcAppraisals, setFcAppraisals] = useState(null);
  useEffect(() => {
    let alive = true;
    // ⚠ HOLD UNTIL THE YEAR IS SETTLED — the rule the policy_fields read 200 lines below
    // already states, and skipping it fired `crop_year=eq.null` three times on first paint
    // and 400'd every one. The `.catch` would have swallowed all three into an empty layer,
    // so the map would simply have drawn every field as unaccounted-for with nothing to say
    // why. Found by rendering the page, not by reading it.
    if (fieldYear == null) { setFcAppraisals(null); return; }
    // ⚠ EVERY COLUMN buildFieldCoverage READS — see NEEDS_COLUMNS. A select that omits
    // `tract` silently demotes every full key to an acreage match; one that omits `fsn`
    // places nothing at all, and the layer then paints a whole book as unaccounted-for.
    sbFetch("silage_appraisals", {
      select: "farmer_id,crop,crop_year,county,appraised_acres,appraised_bu,appraised_qty,fsn,tract,field_no,field_name,unit_number,type_code",
      filters: [`crop_year=eq.${fieldYear}`],
    }).then((r) => { if (alive) setFcAppraisals(r || []); })
      // An absent table must not take the map down — the same rule the FSA-426 panel follows.
      .catch(() => { if (alive) setFcAppraisals([]); });
    return () => { alive = false; };
  }, [fieldYear]);

  // lower(clu_id) → { state, use, supersededAppraisal }, for every insured field of a crop
  // this farm has appraisals on.
  const cluEvidence = useMemo(() => {
    if (!rows || !fcAppraisals || !cluYieldAll) return null;
    const out = {};
    // ⚠ SCOPED TO (FARM, CROP) because buildFieldCoverage is. Running it book-wide would put
    // one farm's farm-number reconciliation against another farm's acres — FSNs are not unique
    // across growers, and two farms sharing a number is exactly how a farm-level match would
    // land on the wrong grower's fields.
    const combos = new Map();
    for (const a of fcAppraisals) {
      const c = String(a.crop || "").toUpperCase();
      if (!a.farmer_id || !c) continue;
      combos.set(`${a.farmer_id}|${c}`, { farmerId: a.farmer_id, crop: c });
    }
    if (!combos.size) return out;
    // The HARVESTED half comes from the yield map the Machine-yield layer already loads, which
    // is why this costs one small query rather than a second pass over jd_field_ftf. It keeps
    // only the most recent harvest per CLU, so it is narrowed to the crop year being drawn —
    // last year's combine pass does not account for this year's field.
    const machine = [];
    for (const [clu, yAll] of Object.entries(cluYieldAll)) {
      // EVIDENCE ONLY — a gone or duplicate pass does not account for a field.
      const y = yAll ? yAll.ev : null;
      if (!y || !y.date || String(y.date).slice(0, 4) !== String(fieldYear)) continue;
      machine.push({ clu_id: clu, metric: "yield", crop: y.crop, acres: 1, mean_value: y.value, operation_date: y.date });
    }
    for (const { farmerId, crop } of combos.values()) {
      const pf = rows.filter((r) => String(r.farmer_id) === String(farmerId));
      if (!pf.length) continue;
      const cov = buildFieldCoverage({
        crop, cropYear: fieldYear, policyFields: pf, appraisals: fcAppraisals.filter((a) => String(a.farmer_id) === String(farmerId)),
        // Machine rows are matched on clu_id inside the rule, so handing it the whole set is
        // safe — a CLU this farm does not insure simply finds no field to attach to.
        machineRows: machine,
      });
      for (const r of cov.rows) {
        out[String(r.cluId || "").toLowerCase()] = {
          state: r.state, use: r.use, supersededAppraisal: r.supersededAppraisal,
          atFarmLevel: !!(r.appraised && r.appraised.atFarmLevel),
        };
      }
    }
    return out;
  }, [rows, fcAppraisals, cluYieldAll, fieldYear]);
  // Save/clear one field's name. source: 'agent' (map/table edit by staff) or 'farmer'
  // (portal). Optimistic local update, then bump the nonce so every consumer refreshes.
  const saveFieldLabel = async (clu, farmerId, label) => {
    const k = (clu || "").toLowerCase(); if (!k) return;
    const v = (label || "").trim();
    try {
      if (v) {
        await sbUpsert("field_labels", [{ clu_id: k, farmer_id: farmerId || null, label: v, source: isPortal ? "farmer" : "agent", updated_by: getCurrentUserEmail(), updated_at: new Date().toISOString() }], "clu_id");
        setLabels(m => ({ ...m, [k]: v }));
      } else if (labels[k]) {
        await sbDelete("field_labels", `clu_id=eq.${encodeURIComponent(k)}`);
        setLabels(m => { const n = { ...m }; delete n[k]; return n; });
      }
    } catch (e) { /* surfaced by the caller's UI if needed */ }
  };

  // Insurance guarantees per farmer, per crop, PER COUNTY. crop_coverage stores one
  // row per (county × crop × plan), so liability/bushel guarantee break out by county
  // exactly like the R&H/NAU Summary of Information. Base rows (RP/YP/APH) carry the
  // real liability; overlays (ECO/SCO/ARPI) sit on the same base acres, so skip them
  // to avoid double-counting. Revenue guarantee = liability (acre-weighted,
  // authoritative); bushel guarantee = liability ÷ price (= SOI "Production Guarantee",
  // verified penny-exact vs MP-0912637's SOI: $97,415 ÷ $4.62 = 21,085 bu).
  //
  // The FETCH is keyed on [] and the price math on a stable KEY, deliberately. This
  // effect used to do both with `[prices]` as its dep, and `prices` is a fresh object
  // literal on every render — the portal renders it as the `prices = {}` default (no
  // prop is passed), the agent app as `{...pricesByYear[y], ...dryBeanPrices}`. So the
  // dep never matched, the effect re-ran, its own setGuar(m) forced another render,
  // and that render minted another `prices` — an unbounded loop that re-downloaded
  // the whole crop_coverage table every cycle for as long as the screen was open.
  // That is the Field Mapper "freeze and crash": not a slow page, a page that never
  // stopped fetching until the tab ran out of memory. It leaves no client_errors row
  // because the tab dies before it can report — see project_portal_page_weight_crashes.
  // Anything derived from `prices` must key on the VALUE, never the object identity.
  const [ccRows, setCcRows] = useState(null);
  useEffect(() => {
    let alive = true;
    sbFetch("crop_coverage", { select:"county,crop,plan,type,practice,price_election,aph_yield,coverage_level,acres,pp_acres,liability,policies(farmer_id,crop_year)" })
      .then(rows => { if (alive) setCcRows(rows || []); })
      .catch(() => { if (alive) setCcRows([]); });
    return () => { alive = false; };
  }, []);
  const pricesKey = JSON.stringify(prices || {});
  const guar = useMemo(() => {
    if (!ccRows) return null;
    // NO GUARANTEES ON A PRIOR-YEAR MAP. crop_coverage is not year-scoped here and
    // holds only the current book forward (CY2026 + the CY2027 rollover today, no
    // CY2025 at all), so pinning it to a prior year's fields would hang THIS year's
    // liability and bushels on last year's ground — a wrong number on a map whose
    // whole job is per-field truth. Null is already a state every consumer handles
    // (it is what shows before ccRows lands), so they draw without the overlay.
    if (offCurrentYear) return null;
    const m = {};
    {
      {
        for (const r of (ccRows||[])) {
          const fid = r.policies && r.policies.farmer_id; if (!fid) continue;
          // ONE CROP YEAR. crop_coverage carries the current book AND the next
          // year's fall-seeded rollover (CY2027 wheat, barley, triticale today).
          // Summing both hung next year's wheat on this year's map: a fall-wheat
          // farm showed two years of wheat liability and bushels in one line.
          // This summary only draws on the current year (offCurrentYear returns
          // null above), so keep only that year's rows.
          if (Number(r.policies.crop_year) !== CURRENT_CROP_YEAR) continue;
          const plan = (r.plan||"").toUpperCase();
          if (plan.includes("ECO") || plan.includes("SCO") || plan === "ARPI") continue; // base plans only
          const crop = r.crop || "?", county = r.county || "—";
          // CORN IS TWO PRODUCTS, AND THIS SUMMARY IS MEANT TO TIE TO THE CARD.
          // crop_coverage is stored per TYPE — Lismore's Steuben corn is one 016 row
          // of 98.5 ac and one 026 row of 4,110.6 — and the farmer card renders those
          // separately. Rolling them up under a bare "CORN" printed 4,209.1 acres of
          // grain-and-silage-in-one against a card that shows the split, and the
          // colour swatch beside it read `fieldCropColor(crop, r.type_code)` with a
          // type_code this function never set, so every corn line drew the muted
          // "type not reported" gold. Grain and silage are different units (bu vs
          // ton), different prices and different end-of-insurance dates; they are not
          // addable. Key on the type; label with cornTypeLabel's rule so an untyped
          // line reads unknown rather than folding into grain.
          const tc = crop === "CORN" ? (String(r.type_code == null ? "" : r.type_code).trim() || null) : null;
          const ck = crop === "CORN" ? `${crop}|${tc || ""}` : crop;
          if (!m[fid]) m[fid] = { byCrop:{} };
          const bc = m[fid].byCrop[ck] || (m[fid].byCrop[ck] = { crop, type_code: tc, liab:0, bu:0, counties:{} });
          // bgSum/bgAcres → acre-weighted production guarantee per acre (APH × coverage),
          // used to split planted vs prevent-plant: planted only gets a bushel guarantee.
          // acres = SOI insured (planted + PP); pp = AUTHORITATIVE prevent-plant acres
          // (crop_coverage.pp_acres, from M13 P11 f[37]='P'). planted = acres − pp.
          const cc = bc.counties[county] || (bc.counties[county] = { liab:0, bu:0, acres:0, pp:0, bgSum:0, bgAcres:0, priceLiabSum:0 });
          const liab = r.liability != null ? Number(r.liability) : null;
          const { price, unit } = getCropPriceAndUnit(r, prices, CURRENT_CROP_YEAR);
          // Divide by the ELECTED price (price x price-election percent), not the
          // price. A CAT row elects 55%, so liability / price alone reads ~45% short.
          const effPrice = electedPrice(price, unit, r);
          const aph = parseFloat(r.aph_yield), cov = parseFloat(r.coverage_level), ac = parseFloat(r.acres);
          if (Number.isFinite(ac)) cc.acres += ac;
          const ppA = parseFloat(r.pp_acres); if (Number.isFinite(ppA)) cc.pp += ppA;
          if (liab != null) {
            bc.liab += liab; cc.liab += liab;
            if (effPrice > 0 && liab > 0) cc.priceLiabSum += effPrice * liab;
          }
          // Bushel guarantee = SOI "Production Guarantee" = APH × coverage × PLANTED acres,
          // sourced ENTIRELY from M13 (aph_yield, coverage_level, acres, pp_acres) — no price
          // election needed (it's null across the whole book). PP acres carry their own
          // PP-factored guarantee and are excluded here. Fall back to liability ÷ price only
          // when M13 has no APH/coverage for the row (rare).
          const plantedAc = Math.max(0, (Number.isFinite(ac) ? ac : 0) - (Number.isFinite(ppA) ? ppA : 0));
          if (Number.isFinite(aph) && Number.isFinite(cov) && plantedAc > 0) {
            // Match the AIP SOI "Marketable Guarantee" to the BUSHEL: RMA rounds the per-acre
            // production guarantee (APH × coverage) to 1 decimal FIRST, then × planted acres.
            // Verified bushel-exact vs Rauberdale's NAU SOI — corn 24,552 / 1,240, soy 7,908,
            // wheat 12,450 (multiplying before rounding was off by 1 bu on corn Livingston).
            const acreGuar = Math.round(aph * cov * 10) / 10;
            const guarBu = acreGuar * plantedAc;
            bc.bu += guarBu; cc.bu += guarBu;
            cc.bgSum += guarBu; cc.bgAcres += plantedAc;
          } else if (liab != null && effPrice > 0) {
            bc.bu += liab / effPrice; cc.bu += liab / effPrice;
          }
        }
      }
    }
    return m;
  }, [ccRows, pricesKey, offCurrentYear]);  // eslint-disable-line
  // WHICH YEARS THE FIELD MAP ACTUALLY HAS. Probed, not assumed, so a year with no
  // field map never becomes a dead dropdown option that renders an empty screen.
  //
  // PostgREST has no DISTINCT, and selecting crop_year outright means downloading a
  // row per field — 28,755 today, and that is the exact cost this screen is here to
  // stop paying. Six one-row probes is bounded, exact, and cheap. It relies on
  // sbFetch honouring `limit`, which it did not until 2026-08-13; before that each
  // of these would have pulled the whole table.
  useEffect(() => {
    let alive = true;
    (async () => {
      const newest = await sbFetch("policy_fields", { select: "crop_year", order: "crop_year.desc", limit: 1 }).catch(() => []);
      const top = Number((newest || [])[0]?.crop_year) || CURRENT_CROP_YEAR;
      const probe = [];
      for (let y = top; y > top - 6; y--) probe.push(y);
      const hits = await Promise.all(probe.map(y =>
        sbFetch("policy_fields", { select: "crop_year", filters: [`crop_year=eq.${y}`], limit: 1 })
          .then(r => ((r || []).length ? y : null)).catch(() => null)));
      if (!alive) return;
      const have = hits.filter(y => y != null);
      // `?? top` matters: if every probe failed (offline mid-load) the screen must
      // still resolve to a year rather than sit on the spinner forever.
      setFieldYears(have.length ? have : [top]);
      setFieldYear(have.includes(CURRENT_CROP_YEAR) ? CURRENT_CROP_YEAR : (have[0] ?? top));
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (fieldYear == null) return;   // hold until the year is settled — never read unscoped
    let alive = true;
    setLoading(true); setErr(null);
    (async () => {
      try {
        // ALWAYS year-scoped. Unfiltered, this loads the same CLU once per year —
        // duplicate fields and doubled acre totals on a page that already ships
        // ~18MB per open.
        // `policies(state)` is the authority for the state printed beside a field's
        // INSURED county — see fieldStateOf. It must be SELECTED, not assumed: omit
        // it and every map sheet silently loses its state again, which is the
        // query-contract failure this repo has already paid for on price_election_pct
        // and on the DRP board's state_code.
        const data = await sbFetch("policy_fields", {
          select: "*,farmers(name),policies(state)", order: "county.asc,id.asc",
          filters: [`crop_year=eq.${fieldYear}`],
        });
        if (alive) setRows(data || []);
      } catch (e) { if (alive) setErr(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [fieldYear]);
  // Boundary set (clu → REP_ACRES). Its own effect keyed on geomNonce so a fresh
  // import can refresh the "has boundary" worklist + per-field acres without a reload.
  const [geomNonce, setGeomNonce] = useState(0);
  // The toolbar used to be ONE wrapping flex row holding 15 controls — search, five
  // multi-selects, a sort dropdown, Clear, and four action buttons — sitting above
  // six KPI tiles and a five-tab switcher. On a laptop that's three or four rows of
  // chrome before any map is visible. Filters and admin actions now live behind two
  // buttons, so the default state is: search, Filters, Actions, view tabs.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    // Per-field acres fallback for fields whose AIP feed carries no acres (R&H PRF),
    // plus the CLU centroids the physical-county classifier needs, plus the set of
    // CLUs that HAVE a boundary. One fetch: these were three separate whole-table
    // reads of the same 22k rows, two of them firing on every mount of this screen.
    // The geometry itself is NOT in this select — that's the 18 MB column, and it's
    // fetched per-CLU by the map panel.
    sbFetch("field_geometries", { select: "clu_id,acres,centroid_lat,centroid_lon" })
      .then(g => {
        const m = {}, s = new Set(), c = {};
        for (const r of (g||[])) {
          if (r.clu_id == null) continue;
          const k = r.clu_id.toLowerCase(); s.add(k);
          if (r.acres != null) m[k] = Number(r.acres);
          if (r.centroid_lat != null && r.centroid_lon != null) c[k] = [Number(r.centroid_lon), Number(r.centroid_lat)];
        }
        if (alive) { setCluAcres(m); setGeomClus(s); setCentroids(c); }
      })
      .catch(() => { if (alive) { setGeomClus(new Set()); setCentroids({}); } });
    return () => { alive = false; };
  }, [geomNonce]);

  // Set of policy-number digits in our whole book — lets the importer tell "R&H
  // boundary for a policy we have, acres not keyed yet → stage it" from "not ours →
  // skip". Fetched once. Also, on load (admin only), promote any staged boundaries
  // whose acres have since been keyed, then refresh so they appear.
  const [knownPolicies, setKnownPolicies] = useState(null);
  useEffect(() => {
    if (isPortal) return;
    let alive = true;
    sbFetch("policies", { select: "policy_number" })
      .then(ps => { if (!alive) return; const s = new Set(); for (const p of (ps||[])) { const d = String(p.policy_number||"").replace(/\D/g,"").replace(/^0+/,""); if (d) s.add(d); } setKnownPolicies(s); })
      .catch(() => { if (alive) setKnownPolicies(new Set()); });
    // reconcile staged boundaries whose acres are now keyed
    (async () => {
      try {
        const token = localStorage.getItem("cloverag_access_token");
        if (!token) return;
        const res = await fetch("/api/market?fn=geomreconcile", { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        const d = await res.json().catch(() => ({}));
        if (alive && d && d.promoted > 0) setGeomNonce(n => n + 1);   // new boundaries → refresh
      } catch (e) { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, [isPortal]);

  // Effective per-field acres: the AIP-reported acres, else the shapefile REP_ACRES by CLU.
  const acreOf = (r) => {
    const a = parseFloat(r.acres); if (Number.isFinite(a)) return a;
    const g = cluAcres[(r.clu_id||"").toLowerCase()]; return Number.isFinite(g) ? g : 0;
  };
  // A field "has a boundary" if its CLU is in field_geometries. Until that set
  // loads (geomClus===null), don't flag anything as missing (avoid false alarms).
  const hasBoundary = (r) => !geomClus ? true : !!(r.clu_id && geomClus.has(r.clu_id.toLowerCase()));

  useEffect(() => { if (initialFarmerId) setExpanded(new Set([initialFarmerId])); }, [initialFarmerId]);
  // ⚠ ARRIVING FROM ANOTHER SCREEN AIMED AT ONE FIELD. The map is the only view that can show a
  // boundary, so the view is switched as well as the focus set — landing on the list with a focus
  // nothing can act on would read as a link that did nothing. Cleared immediately: a one-shot
  // target that persisted would re-jump the map every time the user came back to this page.
  useEffect(() => {
    if (!initialFocus || !initialFocus.cluId) return;
    setView("map");
    if (initialFocus.farmerId) {
      setSelectedFarmerId(initialFocus.farmerId);
      setExpanded(new Set([initialFocus.farmerId]));
    }
    setMapFocus({ farmer_id: initialFocus.farmerId || null, clu_id: initialFocus.cluId });
    onFocusConsumed && onFocusConsumed();
  }, [initialFocus]);   // eslint-disable-line

  // Physical-county resolution: P27 county is the policy's *administrative* county,
  // so border-straddling farms tag fields under the home county even when the
  // boundary sits across the line. Fetch CLU centroids + county polygons and
  // classify each field by where it actually is.
  // Centroids now ride along with the acres/boundary-set fetch above — same table,
  // same 22k rows, no reason to read it twice.
  const [countyPolys, setCountyPolys] = useState(null);

  const nameOf = (r) => (r.farmers && r.farmers.name) || "(unknown)";
  const aipLabel = (a) => a === "RAIN_AND_HAIL" ? "R&H" : (a || "");
  const fmtAc = (x) => (x == null || x === "") ? "—" : Number(x).toLocaleString(undefined, { maximumFractionDigits: 1 });
  const allRows = useMemo(() => (rows || []).filter(r => !_restrictSet || _restrictSet.has(r.farmer_id)), [rows, _restrictSet]);
  // PRF insured acres by policy (prf_policies.total_acres; a farmer reads only their own
  // under RLS), and how many PRF fields each policy has this year — the map summary
  // counts a policy's acres only when all of its fields are in view.
  const [prfPolAc, setPrfPolAc] = useState(null);
  useEffect(() => {
    let alive = true;
    sbFetch("prf_policies", { select: "policy_id,total_acres" })
      .then((rs) => {
        if (!alive) return;
        const m = {};
        for (const r of rs || []) { const a = Number(r.total_acres); if (r.policy_id && Number.isFinite(a) && a > 0) m[r.policy_id] = (m[r.policy_id] || 0) + a; }
        setPrfPolAc(m);
      })
      .catch(() => { if (alive) setPrfPolAc(null); });
    return () => { alive = false; };
  }, [fieldYear]);
  const prfAcres = useMemo(() => {
    if (!prfPolAc) return null;
    const fieldsByPolicy = {};
    for (const r of allRows) if (String(r.crop || "").toUpperCase() === "PRF" && r.acres == null && r.policy_id) fieldsByPolicy[r.policy_id] = (fieldsByPolicy[r.policy_id] || 0) + 1;
    return { byPolicy: prfPolAc, fieldsByPolicy };
  }, [prfPolAc, allRows]);
  // Every CLU this viewer can see, as ONE sorted string. The map panel fetches
  // boundaries for exactly these, and a string keeps the effect from re-firing on
  // every render the way a fresh array would.
  const allClus = useMemo(
    () => [...new Set(allRows.map(r => String(r.clu_id || "").toLowerCase()).filter(Boolean))].sort().join(","),
    [allRows]);
  // lower(clu_id) → insured field, across the WHOLE book (not the active filter) so
  // the importer can match a dropped polygon to its grower/field and dedup correctly.
  const fieldByClu = useMemo(() => {
    const m = new Map();
    for (const r of (rows || [])) {
      if (!r.clu_id) continue;
      const k = r.clu_id.toLowerCase();
      if (!m.has(k)) m.set(k, { farmer: (r.farmers && r.farmers.name) || "(unknown)", fsn: r.fsn, tract: r.tract, field_id: r.field_id, crop: r.crop, acres: r.acres, clu_id: r.clu_id });
    }
    return m;
  }, [rows]);
  // R&H shapefiles carry no FSA GUID — they key on (policy#, FSN, tract, field#).
  // Index those tuples → the insured policy_field (so the importer can resolve the
  // boundary to that field's clu_id). Book-wide, so a bulk multi-farm file resolves
  // each polygon to the right grower by its own policy#. Loose index (no policy) is a
  // fallback used only when a tuple is unambiguous.
  const { fieldByFTF, ftfByLoose } = useMemo(() => {
    const norm = (v) => String(v == null ? "" : v).trim().toUpperCase().replace(/^0+(?=\d)/, "");
    const polD = (v) => String(v == null ? "" : v).replace(/\D/g, "").replace(/^0+/, "");
    const byFTF = new Map(), loose = new Map();
    for (const r of (rows || [])) {
      if (!r.clu_id || !r.fsn || !r.tract || r.field_id == null) continue;
      const f = { farmer: (r.farmers && r.farmers.name) || "(unknown)", fsn: r.fsn, tract: r.tract, field_id: r.field_id, crop: r.crop, acres: r.acres, clu_id: r.clu_id, policy_number: r.policy_number };
      const ftf = `${norm(r.fsn)}|${norm(r.tract)}|${norm(r.field_id)}`;
      if (r.policy_number) { const k = `${polD(r.policy_number)}|${ftf}`; if (!byFTF.has(k)) byFTF.set(k, f); }
      const arr = loose.get(ftf) || []; arr.push(f); loose.set(ftf, arr);
    }
    return { fieldByFTF: byFTF, ftfByLoose: loose };
  }, [rows]);

  // Build a county-polygon index for the states present, then tag every field
  // with its physical county (point-in-polygon on the boundary centroid). Until
  // geometry + polygons load, cty() falls back to the policy/administrative county.
  useEffect(() => {
    const states = [...new Set(allRows.map(r => r.state_fips).filter(Boolean))];
    if (!states.length) return;
    let alive = true;
    Promise.all(states.map(loadCountyGeoForState)).then(results => {
      if (!alive) return;
      const polys = [];
      results.filter(Boolean).forEach(gj => (gj.features || []).forEach(f => {
        if (!f.geometry) return;
        polys.push({ name: (f.properties.NAME || "").toUpperCase().replace(/ COUNTY$/, "").trim(), bbox: _geomBbox(f.geometry), geom: f.geometry });
      }));
      setCountyPolys(polys);
    }).catch(() => {});
    return () => { alive = false; };
  }, [rows]);

  const rowsPhys = useMemo(() => {
    if (!countyPolys || !centroids) return allRows;
    return allRows.map(r => {
      const c = centroids[(r.clu_id || "").toLowerCase()];
      if (!c) return r;
      for (const p of countyPolys) {
        if (c[0] < p.bbox[0] || c[0] > p.bbox[2] || c[1] < p.bbox[1] || c[1] > p.bbox[3]) continue;
        if (_polyContains(c, p.geom)) return { ...r, _physCounty: p.name };
      }
      return r;
    });
  }, [allRows, countyPolys, centroids]);
  // THE COUNTY A FIELD IS INSURED IN WINS, because this screen's own Insurance
  // Summary is keyed on it (it reads crop_coverage) and the rows underneath must add
  // up to the block above them. They did not: PAGEN FARMS' summary printed MONROE
  // 110.6 ac while the Monroe rows beneath summed to 57.7, and Deanna found it.
  //
  // Three different counties exist for one field and they are not interchangeable:
  //   insured_county  the coverage's county (M13 P14) — what the SOI prints, what a
  //                   claim settles at, what an acre of liability belongs to
  //   _physCounty     where the polygon actually sits (point-in-polygon, client-side)
  //   county          the FSA ADMINISTRATIVE county (P27) — the county the FSA FARM
  //                   is filed in, which is what an FSA acreage report needs
  //
  // Pagen's FSN 6949 tract 6064 is FSA county GENESEE throughout while its corn and
  // soybeans are insured in MONROE and its wheat in GENESEE — one tract, two
  // insurance counties, split by crop. No geometry can resolve that and no single FSA
  // county can either; only the coverage says.
  //
  // `_physCounty` stays as the second rung rather than being deleted: it is the only
  // answer for a field whose coverage we could not map, and it was itself added to
  // correct the FSA county. It is also a runtime CDN GeoJSON fetch that can silently
  // fail, which is the other reason the file's own answer goes first.
  const cty = (r) => r.insured_county || r._physCounty || r.county;

  const aipOpts  = useMemo(() => [...new Set(allRows.map(r => r.aip).filter(Boolean))].sort()
    .map(a => ({ value:a, label: a === "RAIN_AND_HAIL" ? "Rain & Hail" : a })), [allRows]);
  const stateOpts = useMemo(() => [...new Set(allRows.map(r => r.state_fips).filter(Boolean))]
    .sort().map(fips => ({ value:fips, label: FIELD_STATE_NM[fips] || fips })), [allRows]);
  // county options narrow to the selected state(s) for a tidier list
  const counties = useMemo(() => [...new Set(rowsPhys
    .filter(r => !stateSet.size || stateSet.has(r.state_fips))
    .map(r => cty(r)).filter(Boolean))].sort(), [rowsPhys, stateSet]);
  const crops    = useMemo(() => [...new Set(allRows.map(r => r.crop).filter(Boolean))].sort(), [allRows]);
  // Corn grain (016=GSG) vs silage (026=GSS) per field, from policy_fields.type_code.
  // Only corn carries this distinction; non-corn rows return null (unaffected by the filter).
  //
  // A MISSING type_code IS NOT GRAIN. This read `type_code === "026" ? Silage : Grain`,
  // so every untyped corn row was asserted to be grain — CY2026 carries 1,307 such
  // fields on 23,692 acres across 25 farmers, none of which the AIP ever typed. That
  // both stated a fact the file doesn't contain AND hid those acres from a "Silage
  // (GSS)" filter, which is exactly the case this filter exists to find. An unknown
  // type must read unknown (CLAUDE.md), so it gets its own bucket and its own colour.
  // Codes beyond 016/026 render raw rather than being folded into grain — the same
  // rule the claim stage codes follow.
  const types    = useMemo(() => [...new Set(allRows.map(cornTypeLabel).filter(Boolean))].sort(), [allRows]);

  // The search box searches IDENTITY — farmer, policy, FSN, tract, field. It is its
  // own predicate because the sticky farm selection keys off it and off NOTHING else;
  // see `selectionActive` below.
  const searchQ = q.trim().toLowerCase();
  const matchesSearch = (r) => {
    if (!searchQ) return true;
    // NOTE: deliberately exclude clu_id — it's a hex GUID, so numeric searches
    // like "6466" false-match GUIDs that merely contain those hex digits.
    const hay = `${nameOf(r)} ${r.policy_number||""} ${r.fsn||""} ${r.field_id||""} ${r.tract||""}`.toLowerCase();
    return hay.includes(searchQ);
  };

  const filtered = useMemo(() => {
    return rowsPhys.filter(r => {
      if (aipSet.size && !aipSet.has(r.aip)) return false;
      if (stateSet.size && !stateSet.has(r.state_fips)) return false;
      if (countySet.size && !countySet.has(cty(r))) return false;
      if (cropSet.size && !cropSet.has(r.crop)) return false;
      if (typeSet.size && !typeSet.has(cornTypeLabel(r))) return false;
      return matchesSearch(r);
    });
  }, [rowsPhys, q, aipSet, stateSet, countySet, cropSet, typeSet]); // eslint-disable-line

  // ── THE SELECTED FARM IS A FILTER, AND AN EMPTY ANSWER MUST READ EMPTY ────────
  // Weather/History/Machine are per-farm, and a sticky selectedFarmerId (set by the
  // picker, by clicking a grower in the tree, or by a deep link from a farmer card)
  // scopes the whole screen, so switching tabs keeps the same farm.
  //
  // The selection falls back to book-wide for exactly ONE reason: the SEARCH BOX has
  // gone somewhere else. Type another grower's name and you get that grower, not an
  // empty table — that is what this test is for.
  //
  // It must NOT fall back because the ATTRIBUTE filters (AIP / state / county / crop /
  // corn type) emptied the farm. This read `filtered.some(...)`, which conflated the
  // two: select a grain-only farm, click the "Silage (GSS)" chip, and the selection
  // silently evaporated — the map, the acreage tree and the KPI strip answered with
  // the WHOLE BOOK's silage. The question asked was "does this farm have silage",
  // the answer shown was every silage field in the agency, and nothing on screen said
  // the farm had been dropped (the picker still named it). Zero fields IS the answer;
  // the banner below says so in words.
  const selectionActive = !!selectedFarmerId && rowsPhys.some(r => r.farmer_id === selectedFarmerId && matchesSearch(r));
  const scopedRows = selectionActive ? filtered.filter(r => r.farmer_id === selectedFarmerId) : filtered;
  const selectedFarmerName = selectedFarmerId ? (nameOf((allRows.find(r => r.farmer_id === selectedFarmerId)) || {}) || null) : null;

  // Every count below is of what is actually ON SCREEN — scoped set, not the book.
  // A strip reading "1,307 fields" over a map drawing none is the same contradiction
  // in a different place.
  const totalAcres = scopedRows.reduce((s, r) => s + acreOf(r), 0);
  // ⚠ ONE WORD, TWO DIFFERENT CLAIMS. `acreOf` answers with the acreage the AIP
  // REPORTED where it has one, and with the CLU BOUNDARY's own measured acreage
  // where it does not. Those are not the same statement: the first is what a
  // premium was charged on and a claim settles at; the second is how big the
  // ground is, whether or not anything is insured on it. Summed under the bare
  // word "Acres" they read as one number a grower will match against their
  // schedule of insurance, and on some farms they cannot match.
  //
  // Measured CY2026: 16,939 of 273,077 acres are boundary-derived (6.2%), across
  // 50 of 233 farms, and 35 farms are >= 10% of their own headline. TWO farms are
  // ENTIRELY boundary-derived — OAKFIELD ALABAMA FARMS (2,009.3 ac, 61 of 61
  // fields) and LAKESHORE DAIRY LLC (1,051.5 ac, 36 of 36) — so their headline
  // contains NO reported acreage at all and must never read as authoritative.
  //
  // THIS SPLITS THE SAME LADDER `acreOf` WALKS AND CHANGES NO VALUE. It exists to
  // NAME the parts, never to alter them: reported + boundary === the total, by
  // construction, and `noAcre` is the third state — a field with neither, which
  // acreOf counts as 0 and which is not a zero-acre field.
  const acreProvenance = useMemo(() => {
    let reported = 0, boundary = 0, nReported = 0, nBoundary = 0, noAcre = 0;
    for (const r of scopedRows) {
      const a = parseFloat(r.acres);
      if (Number.isFinite(a)) { reported += a; nReported++; continue; }
      const g = cluAcres[(r.clu_id || "").toLowerCase()];
      if (Number.isFinite(g)) { boundary += g; nBoundary++; } else noAcre++;
    }
    return { reported, boundary, nReported, nBoundary, noAcre, total: reported + boundary };
  }, [scopedRows, cluAcres]);
  // The one sentence under the headline. Silent on the 183 farms that carry no
  // boundary-derived acre at all — a caveat printed on every farm is a caveat
  // nobody reads — and loudest on the farms where the whole figure is a boundary
  // measurement.
  const acreProvenanceNote = (() => {
    const p = acreProvenance;
    const fieldWord = (n) => `${n} field${n === 1 ? "" : "s"}`;
    const reportNone = (n) => `${fieldWord(n)} report${n === 1 ? "s" : ""} no acreage`;
    if (p.boundary <= 0) return p.noAcre > 0 ? reportNone(p.noAcre) : null;
    // A grower reads the same two quantities in their own words: AIP and CLU are our
    // file names. Same facts, same counts, same branch on "none of it reported".
    if (isPortal) {
      return (p.reported <= 0
        ? `none of these acres are on your schedule of insurance yet — every acre is measured from your field maps (${fieldWord(p.nBoundary)})`
        : `includes ${fmtAc(p.boundary)} ac measured from your field maps, not on your schedule of insurance (${fieldWord(p.nBoundary)})`)
        + (p.reported > 0 && p.noAcre > 0 ? ` · ` + reportNone(p.noAcre) : "");
    }
    if (p.reported <= 0) {
      return `none of this is AIP-reported — every acre is a CLU boundary measurement (${fieldWord(p.nBoundary)})`;
    }
    return `incl. ${fmtAc(p.boundary)} ac measured from CLU boundaries, not reported by the AIP (${fieldWord(p.nBoundary)})`
      + (p.noAcre > 0 ? ` · ` + reportNone(p.noAcre) : "");
  })();
  const kFarmers   = new Set(scopedRows.map(r => r.farmer_id)).size;
  const kCounties  = new Set(scopedRows.map(r => cty(r)).filter(Boolean)).size;
  const kCrops     = new Set(scopedRows.map(r => r.crop).filter(Boolean)).size;

  const groups = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const fid = r.farmer_id || "_none";
      if (!g[fid]) g[fid] = { id: fid, name: nameOf(r), fields: [], acres: 0, noAcre: 0, counties: new Set(), crops: new Set(), aips: new Set() };
      g[fid].fields.push(r);
      g[fid].acres += acreOf(r);
      // ⚠ acreOf() RETURNS 0 FOR A FIELD THE AIP REPORTED NO ACREAGE FOR, which
      // is right for summing and wrong for displaying: measured 2026-09-06, 883 of
      // 15,997 CY2026 policy_fields carry a NULL acreage and NOT ONE carries a real
      // 0. So a farm whose fields are all unreported rendered a confident "0 ac"
      // (24 farms), and 45 more summed a partial figure as if it were the whole.
      // A blank is not a zero — the rule this repo already applies to pp_acres,
      // ui_acres and the LRP coverage bar.
      if (!Number.isFinite(parseFloat(r.acres)) && !Number.isFinite(cluAcres[(r.clu_id || "").toLowerCase()])) g[fid].noAcre += 1;
      if (cty(r)) g[fid].counties.add(cty(r));
      if (r.crop) g[fid].crops.add(r.crop);
      if (r.aip) g[fid].aips.add(r.aip);
    }
    const arr = Object.values(g);
    // Built from `filtered`, NOT `scopedRows`, and deliberately: this is the list the
    // farm picker offers, so scoping it to the selected farm would leave one option
    // and no way to switch. The map and the tree narrow it to the selection
    // themselves, at their render.
    //
    // Each group's .acres already reflects the active crop/state/county filters, so
    // "Acres ↓" with a crop filter gives e.g. "wheat in NY, biggest growers first".
    if (groupSort === "acres_desc") arr.sort((a, b) => (b.acres - a.acres) || a.name.localeCompare(b.name));
    else if (groupSort === "acres_asc") arr.sort((a, b) => (a.acres - b.acres) || a.name.localeCompare(b.name));
    else arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [filtered, groupSort, cluAcres]);

  // ── CHOP PLANNER ────────────────────────────────────────────────────────────
  // "Which fields are you chopping?" answered on the map instead of in a table.
  // Three steps, and the middle one exists because a grower clicking 40 polygons has
  // no way to check their own work: pick → review the acres → send.
  //
  // It is keyed on the INSURED FIELD (CLU), not on a John Deere seeding operation.
  // Measured 2026-08-19: only 13 of 64 portal farms hold a corn_maturity_2026 row
  // (that view is built from JD seedings) while 54 hold corn on this map, so keying
  // on the JD op would have opened an empty picker for 41 farms / 42,775 ac —
  // the dairies among them, who are the farms that actually chop. See hChopSet.
  const [chopStep, setChopStep] = useState(null);   // null | "pick" | "review"
  const [chopSel, setChopSel]   = useState(() => new Set());
  const [chopBusy, setChopBusy] = useState(false);
  const [chopMsg, setChopMsg]   = useState(null);   // { tone, text }
  const [chopSent, setChopSent] = useState(null);   // the receipt after a send

  // The farm being planned. A chop list is ONE farm's — an agent looking at the whole
  // book has to pick a farm first, and a portal user with one farm never sees the ask.
  const chopFarmerId = selectedFarmerId
    || (groups.length === 1 ? groups[0].id : null);
  const chopFarmerName = (groups.find(g => g.id === chopFarmerId) || {}).name || "this farm";

  // Every corn CLU on the map for that farm — the universe the picker works over, and
  // the same set the server will treat as "everything else" under ?replace=1.
  const chopUniverse = useMemo(() => {
    const out = new Map();
    if (!chopFarmerId) return out;
    for (const r of rowsPhys) {
      if (r.farmer_id !== chopFarmerId) continue;
      if (String(r.crop || "").toUpperCase() !== "CORN") continue;
      const k = String(r.clu_id || "").toLowerCase();
      if (!k) continue;
      const ac = r.acres != null ? Number(r.acres) : null;
      const cur = out.get(k);
      // One row per CLU, largest insured line wins — the grower chops the FIELD once.
      if (!cur || (ac || 0) > (cur.acres || 0)) {
        out.set(k, { clu_id: r.clu_id, acres: ac, fsn: r.fsn, tract: r.tract,
                     field: r.field_id, county: r.insured_county || r._physCounty || r.county,
                     type_code: cur && cur.type_code ? cur.type_code : r.type_code || null });
      } else if (cur && !cur.type_code && r.type_code) cur.type_code = r.type_code;
    }
    return out;
  }, [rowsPhys, chopFarmerId]);

  // Seed the picker from what is ALREADY marked, so opening it shows the current plan
  // rather than a blank slate that would wipe the farm's silage on the next save.
  const openChopPicker = async () => {
    if (!chopFarmerId) return;
    setChopMsg(null); setChopSent(null); setChopBusy(true);
    try {
      const r = await authedFetch(`/api/market?fn=jd-clu-fields&farmer=${encodeURIComponent(chopFarmerId)}&year=${fieldYear || CURRENT_CROP_YEAR}&crop=CORN`);
      const j = await r.json();
      const pre = new Set();
      for (const f of (j.fields || [])) {
        if (String(f.harvest_use || "").toLowerCase() === "silage") pre.add(String(f.clu_id || "").toLowerCase());
      }
      setChopSel(pre);
      setChopStep("pick");
    } catch (e) {
      // The picker must not open on a blank slate it cannot vouch for — saving from
      // one would clear a plan the grower never touched.
      setChopMsg({ tone: "err", text: `Couldn't load what's already marked: ${String((e && e.message) || e)}. Not opening the picker — try again.` });
    } finally { setChopBusy(false); }
  };

  const toggleChop = (key) => {
    setChopSel(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  };

  // The picked fields, resolved back to their FSA identity for the review table.
  const chopPicked = useMemo(() => {
    const out = [];
    for (const k of chopSel) { const f = chopUniverse.get(k); if (f) out.push(f); }
    return out.sort((a, b) => (b.acres || 0) - (a.acres || 0));
  }, [chopSel, chopUniverse]);
  const chopAcres = useMemo(() => chopPicked.reduce((s, f) => s + (Number(f.acres) || 0), 0), [chopPicked]);
  // Grain-insured (016) fields being chopped — a change of intent that has to reach
  // the AIP before the crop is put to the other use. Stated, never acted on here.
  // A CHANGE OF INTENT RUNS BOTH WAYS (Kevin, 2026-08-19). Chopping grain-insured
  // corn and combining silage-insured corn both need a Notice of Loss and an
  // appraisal of the STANDING crop before the field is taken. Only the first was
  // caught at first — and the second is the one that hides, because it needs no
  // event: the chopper simply never comes and nothing prompts the call.
  const chopToSilage = useMemo(() => chopPicked.filter(f => f.type_code === "016"), [chopPicked]);
  // Every silage-insured field on the farm, and the ones NOT picked — the reverse case.
  const chopSilageInsured = useMemo(
    () => [...chopUniverse.values()].filter(f => f.type_code === "026"), [chopUniverse]);
  const chopToGrain = useMemo(
    () => chopSilageInsured.filter(f => !chopSel.has(String(f.clu_id).toLowerCase()))
      .sort((a, b) => (b.acres || 0) - (a.acres || 0)),
    [chopSilageInsured, chopSel]);
  const chopToGrainAcres = useMemo(() => chopToGrain.reduce((s, f) => s + (Number(f.acres) || 0), 0), [chopToGrain]);
  // Pick every silage-insured field at once. BRIDGEWATER is 97 fields and SPARTA 74,
  // both insured 100% as silage, so "tap each one" is not a workable answer for the
  // farms this matters most to. It is a SHORTCUT, not a default — nothing is
  // pre-selected, because what a field is insured as is not a statement of what the
  // grower intends to do with it.
  const selectAllSilageInsured = () => setChopSel(prev => {
    const n = new Set(prev);
    for (const f of chopSilageInsured) n.add(String(f.clu_id).toLowerCase());
    return n;
  });

  // Save the selection, then show the summary. ?replace=1 so an UNPICKED field stops
  // being silage — the map shows the whole farm, so the picker is the whole answer.
  const saveChopSelection = async () => {
    if (!chopFarmerId) return;
    setChopBusy(true); setChopMsg(null);
    try {
      const clus = [...chopSel].map(k => (chopUniverse.get(k) || {}).clu_id).filter(Boolean);
      const qs = `farmer=${encodeURIComponent(chopFarmerId)}&year=${fieldYear || CURRENT_CROP_YEAR}&use=silage&replace=1`
        + (clus.length ? `&clus=${encodeURIComponent(clus.join(","))}` : "");
      const r = await authedFetch(`/api/market?fn=jd-chop-set&${qs}`, { method: "POST" });
      const j = await apiJson(r, "the field service");
      setChopStep("review");
      setChopMsg({ tone: "ok", text: `Saved — ${j.fields_set} field${j.fields_set === 1 ? "" : "s"} marked for chopping`
        + (j.maturity_rows_synced ? `, ${j.maturity_rows_synced} maturity row${j.maturity_rows_synced === 1 ? "" : "s"} updated` : "") + "." });
    } catch (e) {
      setChopMsg({ tone: "err", text: `Couldn't save: ${String((e && e.message) || e)}` });
    } finally { setChopBusy(false); }
  };

  const sendChopPlan = async () => {
    if (!chopFarmerId) return;
    setChopBusy(true); setChopMsg(null);
    try {
      const r = await authedFetch(`/api/market?fn=jd-chop-plan&farmer=${encodeURIComponent(chopFarmerId)}&year=${fieldYear || CURRENT_CROP_YEAR}`, { method: "POST" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setChopSent(j);
      // A send that did not actually mail is reported as such rather than as a tick —
      // the grower has to know whether the agent has it.
      const sent = j.emailed && j.emailed.sent !== false;
      setChopMsg({ tone: sent ? "ok" : "warn",
        text: sent ? "Sent to your agent." : "Your plan was saved, but the email did not go out — call the office so they know it's there." });
    } catch (e) {
      setChopMsg({ tone: "err", text: `Couldn't send: ${String((e && e.message) || e)}` });
    } finally { setChopBusy(false); }
  };

  const closeChop = () => { setChopStep(null); setChopMsg(null); setChopSent(null); };



  // Shared comparator so every column header sorts correctly — numeric-aware for
  // FSN/Tract/Field/Acres (so 10 sorts after 2, not after 1), chronological for
  // the ISO planted date, lexical for County/Crop/Farmer.
  const cmpRows = (a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortKey === "acres") return ((parseFloat(a.acres) || 0) - (parseFloat(b.acres) || 0)) * dir;
    if (sortKey === "farmer") return nameOf(a).localeCompare(nameOf(b)) * dir;
    if (sortKey === "county") return (cty(a) || "").localeCompare(cty(b) || "") * dir;
    return (a[sortKey] || "").toString().localeCompare((b[sortKey] || "").toString(), undefined, { numeric: true }) * dir;
  };
  // Scoped, so "Export to Excel" with a farm selected exports THAT FARM. It read
  // `filtered` and shipped the whole book under the selected farm's name.
  const sorted = useMemo(() => [...scopedRows].sort(cmpRows), [scopedRows, sortKey, sortDir]);

  // Missing-boundary worklist: insured fields (respecting the active filters AND the
  // selected farm) whose CLU has no row in field_geometries — i.e. we can't draw them
  // and need a shapefile. A field with NO clu_id can never take a boundary (nothing to
  // key it on) — that's grapes/orchards, which the AIP feed carries without a CLU.
  // Exclude those so the worklist only shows the actionable fields (has a CLU, just no
  // boundary yet).
  const missing = useMemo(() => {
    if (!geomClus) return [];
    return scopedRows.filter(r => r.clu_id && !hasBoundary(r))
      .sort((a, b) => acreOf(b) - acreOf(a) || nameOf(a).localeCompare(nameOf(b)));
  }, [scopedRows, geomClus, cluAcres]);
  const missingAcres = useMemo(() => missing.reduce((s, r) => s + acreOf(r), 0), [missing]);
  const missingFarms = useMemo(() => new Set(missing.map(r => r.farmer_id)).size, [missing]);
  // Fields with no CLU at all (grapes/orchards) — surfaced as a footnote count, not
  // hidden silently, so it's clear WHY they're excluded rather than looking lost.
  const noCluCount = useMemo(() => scopedRows.filter(r => !r.clu_id).length, [scopedRows]);

  const toggle = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setSort = (k) => { if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };

  // Open-card resolution: the field's farmer_id is the raw farmers UUID, but the
  // frontend farmer card's id is its primary *policy* id — so match by policy
  // number (then name) to the book farmer and select that id.
  const normName = (s) => (s || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  const bookLookup = useMemo(() => {
    const byPn = {}, byName = {};
    for (const f of (farmers || [])) {
      const pns = f._allPolicyNums || (f.policyNumber ? [f.policyNumber] : []);
      pns.forEach(pn => { if (pn) byPn[pn] = f.id; });
      if (f.name) byName[normName(f.name)] = f.id;
    }
    return { byPn, byName };
  }, [farmers]);
  const resolveBookId = (g) => {
    for (const fld of g.fields) { if (fld.policy_number && bookLookup.byPn[fld.policy_number]) return bookLookup.byPn[fld.policy_number]; }
    return bookLookup.byName[normName(g.name)] || null;
  };

  // Export the current filtered + sorted table to .xlsx (SheetJS, loaded on demand).
  const exportXlsx = async () => {
    if (!window.XLSX) {
      await new Promise((res, rej) => { const s = document.createElement("script"); s.src = "https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js"; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
    }
    const data = sorted.map(r => ({
      Farmer: nameOf(r), AIP: aipLabel(r.aip), Policy: r.policy_number || "", County: cty(r) || "",
      FSN: r.fsn || "", Tract: r.tract || "", Field: r.field_id || "", Crop: r.crop || "",
      Planted: r.report_date || "", Acres: r.acres == null ? "" : Number(r.acres), CLU: r.clu_id || "", CropYear: r.crop_year || "",
      // Month precision (P27 #20) — exported as YYYY-MM so a spreadsheet cannot
      // render a day the AIP never sent.
      CIMSAsOfMonth: r.clu_release_date ? String(r.clu_release_date).slice(0, 7) : "",
    }));
    const ws = window.XLSX.utils.json_to_sheet(data);
    const wb = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(wb, ws, "Fields");
    window.XLSX.writeFile(wb, `field-data-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // Export the missing-boundary worklist (farmer + Farm/Tract/Field + acres).
  const exportMissingXlsx = async () => {
    if (!window.XLSX) {
      await new Promise((res, rej) => { const s = document.createElement("script"); s.src = "https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js"; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
    }
    const data = missing.map(r => ({
      Farmer: nameOf(r), AIP: aipLabel(r.aip), Policy: r.policy_number || "", County: cty(r) || "",
      FSN: r.fsn || "", Tract: r.tract || "", Field: r.field_id || "", Crop: r.crop || "",
      Acres: acreOf(r) > 0 ? acreOf(r) : "", CLU: r.clu_id || "",
    }));
    const ws = window.XLSX.utils.json_to_sheet(data);
    const wb = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(wb, ws, "Missing boundaries");
    window.XLSX.writeFile(wb, `missing-boundaries-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const card = { background:"#fff", border:"1px solid #d9e4d9", borderRadius:10, padding:"14px 16px" };
  const sel  = { padding:"7px 10px", borderRadius:8, border:"1px solid #cdd9cd", background:"#fff", fontSize:13, color:"#1a2e1a" };

  const Th = ({ k, children, right }) => (
    <th onClick={() => setSort(k)} style={{ padding:"8px 10px", textAlign: right?"right":"left", cursor:"pointer", whiteSpace:"nowrap",
      fontSize:11, fontWeight:800, letterSpacing:"0.04em", textTransform:"uppercase", color:"#4a6e4a", borderBottom:"2px solid #d9e4d9", userSelect:"none" }}>
      {children}{sortKey===k ? (sortDir==="asc"?" ▲":" ▼") : ""}
    </th>
  );
  const fmtPlanted = (d) => d ? `${d.slice(5,7)}/${d.slice(8,10)}/${d.slice(0,4)}` : "—";
  // P27 #20, whose RMA rule calls it a "CimsAsOfDate": the as-of date of the CIMS
  // snapshot the AIP pulled this field's CLU from, bounded to 06/01 of the prior
  // RY through 12/01 of the current RY.
  //
  // WHAT IT IS NOT: the date CIMS was RUN. CIMS's own layout separates the two —
  // CIMS_ARDIFF_* carries field 4 "CIMS Load Date" (when the comparison ran) AND
  // field 84 "CIMS CLU As of Date" (this one). Only the as-of date reaches the
  // M13 file. Verified across every field of every record type in both live
  // files: P27 #20 is the ONLY populated CIMS field — P14 #31 CIMS Information
  // Code and P27 #25 FSA RMA Acreage Difference Code are both 0%, and no CIMS
  // Load Date field exists in M13 at all.
  //
  // So this answers "which CIMS snapshot was this field keyed against", which is
  // useful for an audit, and it does NOT answer "when was CIMS last run against
  // this policy" — that lives on the ARDIFF report the AIP receives and we do not.
  //
  // MONTH precision: the file sends MMYYYY and the column stores the 1st, so
  // never print a day here.
  const CLU_MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const fmtCluVintage = (d) => {
    if (!d) return "—";
    const y = +String(d).slice(0, 4), m = +String(d).slice(5, 7);
    return (m >= 1 && m <= 12 && y > 1900) ? `${CLU_MON[m - 1]} ${y}` : "—";
  };
  // Months between the vintage and today. Shown as a plain number beside the
  // date, NOT as a "stale" badge — where the line falls depends on the county
  // and on when FSA last re-flew it, which is the agent's call, not the app's.
  const cluAgeMonths = (d) => {
    if (!d) return null;
    const y = +String(d).slice(0, 4), m = +String(d).slice(5, 7);
    if (!(m >= 1 && m <= 12 && y > 1900)) return null;
    const now = new Date();
    return (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
  };
  const fieldCells = (r) => {
    // MPCI (corn/soy/wheat/…) shows REPORTED acres from the M13 (policy_fields.acres = P27
    // f[22]); when a field isn't reported yet it stays blank — never show CLU acres as if
    // insured. ONLY PRF (whose R&H M13 P27 carries no per-field acres) falls back to the
    // CLU boundary acreage (field_geometries), which is all we have per-field for PRF.
    const isPrf = (r.crop || "").toUpperCase() === "PRF";
    const rowAc = r.acres != null ? Number(r.acres) : (isPrf ? (cluAcres[(r.clu_id || "").toLowerCase()] ?? null) : null);
    const ppAc = r.pp_acres != null ? Number(r.pp_acres) : 0;
    const totAc = rowAc != null ? rowAc : 0;
    const plantedAc = Math.max(0, totAc - ppAc);
    const ppFull = ppAc > 0 && plantedAc <= 0.05;
    return (
      <>
        <td style={td}>{cty(r) || "—"}</td>
        <td style={td}>{r.fsn || "—"}</td>
        <td style={td}>{r.tract || "—"}</td>
        <td style={td}>{r.field_id || "—"}</td>
        <td style={td}>{r.crop ? fmCropName(r.crop) : <span style={{ color:"#b08", fontStyle:"italic" }}>unreported</span>}</td>
        <td style={td}>{ppFull ? <span style={{ color:"#9a5a1a", fontWeight:700 }}>⬚ prevent plant</span> : fmtPlanted(r.report_date)}</td>
        <td style={{ ...td, whiteSpace:"nowrap", color:"#5c6f5c" }}
            title={r.clu_release_date
              ? `CIMS snapshot this field's CLU was keyed from (P27 #20 CimsAsOfDate)${r.clu_determination_code ? ` · determination ${r.clu_determination_code}` : ""}. NOT the date CIMS was run — M13 does not carry one.`
              : "the AIP did not report a CIMS as-of date for this field"}>
          {fmtCluVintage(r.clu_release_date)}
          {cluAgeMonths(r.clu_release_date) != null && (
            <span style={{ color:"#677167", fontSize:11 }}> · {cluAgeMonths(r.clu_release_date)}mo</span>
          )}
        </td>
        <td style={{ ...td, textAlign:"right", fontVariantNumeric:"tabular-nums" }} title={ppAc > 0 ? `${plantedAc.toFixed(1)} planted + ${ppAc.toFixed(1)} PP` : undefined}>{fmtAc(rowAc)}</td>
        <td style={{ ...td, textAlign:"right", fontVariantNumeric:"tabular-nums", color: ppAc > 0 ? "#9a5a1a" : "#ccc", fontWeight: ppAc > 0 ? 700 : 400 }}>{ppAc > 0 ? fmtAc(ppAc) : "—"}</td>
      </>
    );
  };

  // Per-grower insurance summary — a schedule-of-insurance roll-up that mirrors the
  // R&H/NAU SOI: each crop broken out by county with acres (from the field rows) +
  // liability + bushel guarantee (from crop_coverage, per county). Counties are the
  // union of those the fields touch and those carrying coverage, so a county with
  // liability but no acreage reported yet still shows (and vice-versa). Returns, per
  // crop: a sorted list of county rows {county, acres, liab, bu} + crop totals.
  const buildCropSummary = (g) => {
    // Schedule-of-insurance roll-up sourced ENTIRELY from crop_coverage (the AIP M13
    // feed) — acres, prevent-plant, liability, bushel guarantee all per county. PP is
    // AUTHORITATIVE (crop_coverage.pp_acres = P11 f[37]='P'); NO field-date heuristic.
    // acres = planted + PP (SOI insured), so planted = acres − pp.
    const gg = guar && guar[g.id];
    if (!gg) return [];
    // The map key is crop-plus-type for corn (see the `guar` memo); `gc.crop` is the
    // crop and `gc.type_code` its RMA type, so the row can print "CORN · Silage (GSS)"
    // and tint its swatch the same brown the map draws that field in.
    return Object.entries(gg.byCrop).map(([key, gc]) => {
      const crop = gc.crop || key;
      const rows = Object.entries(gc.counties).map(([cn, ins]) => {
        const total = ins.acres || 0;
        const pp = ins.pp || 0;
        const planted = Math.max(0, total - pp);
        // Liability & bushel guarantee are the M13 SOI figures per county
        // (crop_coverage.liability, base MPCI). When a county has PP the M13 liability
        // is the BLENDED coverage total (planted + PP acres) — we show it as-is rather
        // than fabricating a split, so the numbers always tie to the SOI.
        const liab = ins.liab > 0 ? ins.liab : null;
        const bu   = ins.bu   > 0 ? ins.bu   : null;
        return { county: cn, acres: planted, ppAcres: pp, liab, bu };
      }).filter(r => r.acres > 0 || r.ppAcres > 0 || r.liab || r.bu)
        .sort((a,b)=> ((b.acres+b.ppAcres) - (a.acres+a.ppAcres)) || ((b.liab||0) - (a.liab||0)) || a.county.localeCompare(b.county));
      const acres = rows.reduce((s,r)=> s + r.acres, 0);                  // planted only
      const ppAcres = rows.reduce((s,r)=> s + (r.ppAcres||0), 0);
      const liab = rows.reduce((s,r)=> s + (r.liab||0), 0);
      const bu   = rows.reduce((s,r)=> s + (r.bu||0), 0);
      // `typeLabel` is null for every crop but corn, so nothing else grows a suffix.
      return { key, crop, type_code: gc.type_code || null,
        typeLabel: cornTypeLabel({ crop, type_code: gc.type_code }),
        rows, acres, ppAcres, ppLiab: null,
        liab: liab > 0 ? liab : null, bu: bu > 0 ? bu : null };
    }).sort((a,b)=> ((b.acres+b.ppAcres) - (a.acres+a.ppAcres)) || ((b.liab||0) - (a.liab||0)) || a.crop.localeCompare(b.crop));
  };

  // Print/Save-PDF a field-inventory report for one grower (new window → print).
  const printFieldReport = (g) => {
    const rows = g.fields.slice().sort((a,b) =>
      (a.policy_number||"").localeCompare(b.policy_number||"") ||
      (cty(a)||"").localeCompare(cty(b)||"") ||
      (a.field_id||"").localeCompare(b.field_id||"", undefined, {numeric:true}));
    const totalAc = g.fields.reduce((s,r) => s + acreOf(r), 0);
    // The Acres COLUMN below prints `r.acres` — reported, or a dash. The TOTAL runs
    // through acreOf, which substitutes the CLU boundary's acreage where the AIP
    // reported none. So on a farm with any such field the rows cannot sum to their
    // own footer, and on OAKFIELD ALABAMA every row is a dash under a 2,009.3 total.
    // Say which quantity the footer is rather than leaving the reader to reconcile
    // a document that does not add up.
    const fieldsFromBoundary = g.fields.filter(r => !Number.isFinite(parseFloat(r.acres)) && acreOf(r) > 0).length;
    const fieldTotalIsMapped = fieldsFromBoundary > 0;
    const esc = (s) => String(s==null?"":s).replace(/[&<>]/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;"}[c]));
    const body = rows.map(r => {
      const ppAc = r.pp_acres != null ? Number(r.pp_acres) : 0;
      const plantedAc = Math.max(0, (r.acres!=null?Number(r.acres):0) - ppAc);
      const ppFull = ppAc > 0 && plantedAc <= 0.05;
      return `<tr><td>${esc(cty(r))||"—"}</td><td>${esc(r.fsn)||"—"}</td><td>${esc(r.tract)||"—"}</td><td>${esc(r.field_id)||"—"}</td><td>${esc(r.crop)||"—"}</td><td>${ppFull?'&#9633; prevent plant':fmtPlanted(r.report_date)}</td><td class="r">${r.acres!=null?Number(r.acres).toFixed(1):"—"}</td><td class="r">${ppAc>0?ppAc.toFixed(1):"—"}</td></tr>`;
    }).join("");

    // Insurance summary — schedule-of-insurance roll-up by crop × county (mirrors the SOI)
    const summ = buildCropSummary(g);
    const liabTxt = (v) => v != null ? "$" + Math.round(v).toLocaleString() : "—";
    const buTxt   = (v) => v != null ? Math.round(v).toLocaleString() + " bu" : "—";
    const totLiab = summ.reduce((s,r)=> s + (r.liab||0), 0);
    const totPlanted = summ.reduce((s,r)=> s + r.acres, 0);
    const totPP   = summ.reduce((s,r)=> s + (r.ppAcres||0), 0);
    const totPPLiab = summ.reduce((s,r)=> s + (r.ppLiab||0), 0);
    const summBody = summ.map(r => {
      const single = r.rows.length <= 1;
      const only = r.rows[0];
      // Corn prints its grain/silage type beside the crop, matching the screen and
      // the farmer card — they are separate SOI lines in different units.
      const head = `<tr class="croprow"><td>${esc(r.crop)}${r.typeLabel ? ` <span style="font-weight:400;color:#60735f">· ${esc(r.typeLabel)}</span>` : ""}</td><td>${single ? esc(only ? only.county : "—") : r.rows.length+" counties"}</td><td class="r">${fmtAc(r.acres)}</td><td class="r">${liabTxt(r.liab)}</td><td class="r">${buTxt(r.bu)}</td></tr>`;
      const subs = single ? "" : r.rows.map(cr => {
        const cpp = cr.ppAcres > 0 ? `<tr class="ctyrow"><td></td><td class="indent" style="padding-left:2.4em;color:#9a5a1a;font-weight:600">&#9633; ${esc(cr.county)} Prevent Plant</td><td class="r" style="color:#9a5a1a">${fmtAc(cr.ppAcres)}</td><td class="r">—</td><td class="r">—</td></tr>` : "";
        return `<tr class="ctyrow"><td></td><td class="indent">${esc(cr.county)}</td><td class="r">${fmtAc(cr.acres)}</td><td class="r">${liabTxt(cr.liab)}</td><td class="r">${buTxt(cr.bu)}</td></tr>${cpp}`;
      }).join("");
      // Single-county crop: PP belongs to that one county.
      const pp = (single && r.ppAcres > 0) ? `<tr class="ctyrow"><td></td><td class="indent" style="color:#9a5a1a;font-weight:600">&#9633; Prevent Plant${r.rows[0] ? " · "+esc(r.rows[0].county) : ""}</td><td class="r" style="color:#9a5a1a">${fmtAc(r.ppAcres)}</td><td class="r">—</td><td class="r">—</td></tr>` : "";
      return head + subs + pp;
    }).join("");
    const ppTotRow = totPP > 0 ? `<tr style="color:#9a5a1a;font-weight:700"><td>&#9633; Prevent Plant</td><td></td><td class="r">${fmtAc(totPP)}</td><td class="r">—</td><td class="r">—</td></tr>` : "";
    const summTable = summ.length ? `
      <div class="sech">Insurance Summary</div>
      <table class="summ"><thead><tr><th>Crop</th><th>County</th><th class="r">Acres</th><th class="r">Liability</th><th class="r">Bu Guarantee</th></tr></thead>
      <tbody>${summBody}</tbody>
      <tfoot><tr><td>Total${totPP>0?" planted":""}</td><td>${g.counties.size} ${g.counties.size===1?"county":"counties"}</td><td class="r">${fmtAc(totPlanted)}</td><td class="r">${liabTxt(totLiab>0?totLiab:null)}</td><td class="r">—</td></tr>${ppTotRow}</tfoot></table>
      <div class="note">Liability &amp; bushel guarantee = base MPCI (RP/YP) per county, from the SOI (M13); SCO/ECO overlays excluded.${totPP>0?" For counties with prevent plant, the liability is the blended SOI figure covering planted + PP acres." : ""}</div>
      <div class="sech">Field Detail</div>` : "";

    const w = window.open("", "_blank"); if (!w) { alert("Pop-up blocked — allow pop-ups to print the report."); return; }
    writePrintWindow(w, `<!doctype html><html><head><meta charset="utf-8"><title>${esc(g.name)} — Field Report</title><style>
      body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1a2e1a;padding:32px;}
      h1{font-size:20px;margin:0 0 3px;} .sub{color:#60735f;font-size:12px;margin-bottom:18px;}
      table{width:100%;border-collapse:collapse;font-size:12px;} .r{text-align:right;}
      th{text-align:left;text-transform:uppercase;font-size:9px;letter-spacing:.05em;color:#4a6e4a;border-bottom:2px solid #d9e4d9;padding:6px 8px;}
      td{padding:5px 8px;border-bottom:1px solid #eef2ee;} tfoot td{font-weight:800;border-top:2px solid #d9e4d9;border-bottom:none;}
      .sech{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#4a6e4a;margin:18px 0 6px;}
      table.summ{margin-bottom:6px;} table.summ .croprow td{font-weight:700;border-top:1px solid #e3ebe3;border-bottom:none;}
      table.summ .ctyrow td{border-bottom:none;color:#60735f;padding:2px 8px;} table.summ .indent{padding-left:18px;}
      .note{font-size:10px;color:#677167;margin:2px 0 0;}
      @media print{body{padding:0;}}
    </style></head><body>
      <h1>${esc(g.name)}</h1>
      <div class="sub">Schedule of insurance · ${g.fields.length} fields · ${esc([...g.counties].join(", "))} · ${esc([...g.crops].join(", "))}</div>
      ${summTable}
      <table><thead><tr><th>County</th><th>FSN</th><th>Tract</th><th>Field</th><th>Crop</th><th>Planted</th><th class="r">Acres</th><th class="r">PP ac</th></tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr><td colspan="6">Total${fieldTotalIsMapped ? " (mapped)" : ""}</td><td class="r">${totalAc.toFixed(1)} ac</td><td class="r">${(g.fields.reduce((s,r)=>s+(r.pp_acres!=null?Number(r.pp_acres):0),0)).toFixed(1)}</td></tr></tfoot></table>
      ${fieldTotalIsMapped ? `<div class="sub" style="margin-top:6px">The Acres column is what the AIP reported for each field; a dash means it reported none.
        The total adds the CLU boundary's own measured acreage for those ${fieldsFromBoundary} field${fieldsFromBoundary === 1 ? "" : "s"},
        so it is MAPPED acreage and does not equal the column above it. Insured acreage is the Insurance Summary.</div>` : ""}
      <script>window.onload=function(){setTimeout(function(){window.print();},150);}</script>
    </body></html>`);
  };
  const td = { padding:"6px 10px", borderBottom:"1px solid #eef2ee", fontSize:13, color:"#1f3320" };

  // Open a field sub-page (weather/PRF): remember the view we came from + sticky the
  // field's farm, so Back returns us to the same tab/map spot instead of the top.
  // The crop year rides along: the field's page reads "this season" off it, and a field
  // opened from a prior year's map must not run "Since planting" into this one.
  const openWeather = (f) => { if (f && f.farmer_id) setSelectedFarmerId(f.farmer_id); setWeatherField(f ? { ...f, cropYear: fieldYear || CURRENT_CROP_YEAR } : f); };
  // An Acreage row opens THAT field on the map — on a phone, its field page, which carries
  // Weather beside the field's maps and coverage. Kevin, 2026-11-01: a Weather button on
  // every Acreage row repeated the Weather tab; the row itself is now the way in.
  const openAcreageField = (f) => {
    if (!f) return;
    const focus = { farmer_id: f.farmer_id || null, clu_id: f.clu_id || null,
      lat: f.latitude != null ? Number(f.latitude) : null, lon: f.longitude != null ? Number(f.longitude) : null };
    setMapFocus(focus); setView("map");
  };
  const openPRF     = (f) => { if (f && f.farmer_id) setSelectedFarmerId(f.farmer_id); setPrfField(f); };
  // Back from a field sub-page → restore the prior tab. For the map, the FieldMapPanel
  // remounts at the EXACT center/zoom captured in mapViewportRef (initialView), landing
  // you right back where you were — no forced re-zoom.
  // Weather/PRF render as an OVERLAY on top of the still-mounted mapper (the map is
  // never unmounted), so Back just hides the overlay and the map is exactly as you left
  // it — same zoom/pan, no reload. No view change needed (the tab underneath is intact).
  const backToMapper = () => { setWeatherField(null); setPrfField(null); };

  // `selectionActive` / `scopedRows` / `selectedFarmerName` are defined up beside
  // `filtered`, because the KPI strip, the export and the boundary worklist all read
  // the scoped set now — they used to read `filtered` and answer book-wide under a
  // selected farm's name.

  /* ⚠ A SEARCH THAT LEAVES ONE FARM ON THE MAP *IS* A FARM SELECTION.
   *
   * Kevin, 2026-10-06: "If I select a farmer in field mapper and then I want to see
   * machine data, I have to select the farm again, thats unnecessary redundancy."
   *
   * REPRODUCED, which is what settled where the redundancy actually was — the farm
   * PICKER already carried through fine (pick AUSTIC, open Machine Data, it opens on
   * AUSTIC). The route that did not was the SEARCH BOX, and that is the natural way
   * to find a farmer: type "PANEK", the map narrows to Panek, the header reads one
   * farm — and Machine Data said "Pick a farm above to see its planting, harvest,
   * yield maps". The farm was on screen and the tab asked for it again.
   *
   * Searching is not the same STATE as picking (`selectedFarmerId` is deliberately
   * untouched by the search, so Weather and History keep their sticky farm), but
   * where the filters leave exactly ONE farmer on the map there is nothing to choose
   * between — that farm is what the screen is showing.
   *
   * ⚠ EXACTLY ONE, NEVER "THE FIRST". Two farms left by a search is a genuine
   * ambiguity and the tab must keep asking; picking the first would open machine data
   * for a farm the agent did not mean and say nothing about it. Same rule the chop
   * plan and the acre lattice follow elsewhere in this file.
   *
   * It stays OUT of `selectedFarmerId` for the reason the original comment gives: a
   * farm resolved for one tab must not silently narrow the map and acreage on all the
   * others. The picker already renders `machineFarmerId` while the machine tab is up,
   * so the toolbar says which farm is being shown.
   */
  /* The farms the current filters leave on screen, with their names — capped,
   * because the answer only matters while it is small enough to choose from. A
   * book-wide view leaves 233 farms and nobody is picking from that list; what
   * matters is the handful a search narrowed to. */
  const FARM_CHOICE_CAP = 12;
  const filteredFarms = useMemo(() => {
    const seen = new Map();
    for (const r of filtered) {
      const id = r && r.farmer_id;
      if (!id || seen.has(id)) continue;
      seen.set(id, { id, name: nameOf(r) || "Farm" });
      if (seen.size > FARM_CHOICE_CAP) break;
    }
    return [...seen.values()];
  }, [filtered]); // eslint-disable-line
  const soleFilteredFarmId = filteredFarms.length === 1 ? filteredFarms[0].id : null;
  // THE PICK IS DECIDED BY WHO HAS MACHINE DATA. Kevin, 2026-10-17, on the Machine
  // Data tab under "All farms": "None of these farmers have machine data." The
  // chooser below was built for an AMBIGUOUS SEARCH (two farms, one name) and the
  // 12-farm cap let it fire on the whole book too, offering the first thirteen farms
  // in file order. This is every farm on screen that is actually connected and
  // carrying records — 69 farms book-wide on 2026-10-17, so the list scrolls
  // rather than being cut to the first few.
  const jdFarmsOnScreen = useMemo(() => {
    if (!jdDataSet || !jdDataSet.size) return [];
    const seen = new Map();
    for (const r of filtered) {
      const id = r && r.farmer_id;
      if (!id || seen.has(id) || !jdDataSet.has(id)) continue;
      seen.set(id, { id, name: nameOf(r) || "Farm" });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered, jdDataSet]); // eslint-disable-line

  // Machine Data opens on a real farm rather than an empty page: a portal login
  // covering several entities lands on the first one that HAS machine records.
  // Hoisted above the toolbar so the ONE picker can show what that tab is actually
  // displaying. Deliberately NOT written into selectedFarmerId — a default chosen
  // for one tab must not silently narrow the map and acreage on all the others.
  const machineFarmerId = selectedFarmerId
    || soleFilteredFarmId
    || (isPortal ? (((groups.find(g => jdDataSet && jdDataSet.has(g.id)) || groups[0]) || {}).id || null) : null);
  // ⚠ THAT DEFAULT IS THE MACHINE TAB'S, AND ONLY ITS. Every other page shows the farm
  // somebody picked, or the one farm the filters left on screen — never "the first farm
  // with machine data". The phone map's picker read machineFarmerId, so a grower with
  // two entities (Panek Farms + Alan Panek) saw "Panek Farms" over a map drawing both,
  // and picking "All farms" changed nothing because the label never came from it.
  const shownFarmId = selectedFarmerId || soleFilteredFarmId;
  // The grower's OWN farms, from every field they have, not from `filtered`: a search or
  // a crop chip that left one entity on screen used to hide the picker and retitle the
  // page with that entity, leaving no way back but clearing the filter. Agents keep the
  // filtered list (a crop filter narrowing the book's farm list is how they use it).
  const portalFarms = useMemo(() => {
    if (!isPortal) return [];
    const seen = new Map();
    for (const r of allRows) { const id = r && r.farmer_id; if (id && !seen.has(id)) seen.set(id, { id, name: nameOf(r) || "Farm" }); }
    return [...seen.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [isPortal, allRows]); // eslint-disable-line
  const pickerGroups = isPortal ? portalFarms : groups;
  const showFarmPicker = !(isPortal && portalFarms.length <= 1);
  const soleOwnFarm = isPortal && portalFarms.length === 1 ? portalFarms[0] : null;
  const fmWhoseFields = selectionActive && selectedFarmerName ? selectedFarmerName
    : soleOwnFarm ? (soleOwnFarm.name || "Your fields") : isPortal ? "Your fields" : "All farms";
  // A new farm on a PHONE drops the crop chips: they live inside the field list, which
  // shows chips only for crops the farm grows, so a "Wheat" picked on one farm stayed
  // on — invisible — over a farm with none, and the map read "Clear a filter" with
  // nothing on screen to clear. (The desktop shows its crop filter in the toolbar.)
  const pickFarm = (id) => {
    const nid = id || null;
    if (isMobile && nid !== selectedFarmerId) setCropSet(new Set());
    setSelectedFarmerId(nid);
  };

  // Field weather / PRF pages — rendered as a full-screen overlay (below) so the map
  // underneath stays mounted and instantly correct on Back. The overlay is a
  // flex column: on mobile a sticky dark Back bar (safe-area padded so it clears
  // the notch/URL bar and never scrolls out of reach) + scrolling body; on
  // desktop the child keeps its own inline Back button.
  // Portalled to <body>: inside the shell's scrolling content pane a fixed layer is
  // trapped by iOS momentum scrolling and the page underneath scrolls instead.
  // A portal leaves the shell, and with it the shell's font: name it here or the page
  // falls back to the browser's serif (what Kevin saw on the machine data sheet).
  const fieldOverlay = { position:"fixed", inset:0, zIndex:2000, background:"#f2f6f2", display:"flex", flexDirection:"column", fontFamily:T.sans, color:T.ink };

  // ── PHONE FIELD MAPPER (2026-10-31) ───────────────────────────────────────────
  // On a phone the Map tab is the whole screen: search, year and farm float on the
  // map, and the sheet carries the summary, the list and each field's page. The other
  // tabs keep their own layout and are one tap away from the sheet. The chop picker
  // stays in it too: its panel takes the sheet's place (phone.chopPanel).
  const fmImmersive = !!isMobile && view === "map" && !loading && !err && allRows.length > 0;
  const fmYear = fieldYear || CURRENT_CROP_YEAR;
  const fmNonCrop = aipSet.size + stateSet.size + countySet.size + typeSet.size;
  // Every other tab, on a phone, is a page OFF the map: one header (‹ Map, title, whose
  // fields and which year), and none of the desktop title, figures strip, tab row or
  // toolbar stacked above it. The map sheet's buttons are how you get here.
  const fmPage = !!isMobile && view !== "map" && !chopStep && !loading && !err && allRows.length > 0;
  const FM_PAGE_TITLES = { verify: "My acres", tree: "Acreage", rainfall: "Weather", history: "History",
    missing: "Boundaries", machine: "Machine data", soil: "Soil samples" };
  // ── SYSTEM BACK CLOSES THE PAGE, NOT THE APP ────────────────────────────────
  // A phone's back swipe or button used to leave Silas from any Field Mapper page.
  // Opening a page (a tab, a field's Weather or PRF page, machine data) adds one
  // history step; Back takes it, and the in-app ‹ buttons spend the same step.
  //
  // ⚠ THE MAP'S OWN SHEETS COUNT TOO. A field's sheet, the field list and Layers were
  // not pages here, so tapping a field and pressing Back — to close it — closed Silas,
  // and Back in the chop picker threw away the picks. FieldMapPanel reports whether one
  // of its sheets is open (phone.onSubChange) and lends a closer (phone.subCloseRef).
  // Each Back closes the TOPMOST thing only; if something is still open underneath,
  // the step is pushed again (fmPopNonce) so the next Back closes that.
  const fmPushedRef = useRef(false);
  const [fmSubOpen, setFmSubOpen] = useState(false);   // a phone map sheet (field / list / Layers) is up
  const fmSubCloseRef = useRef(null);
  const [fmPopNonce, setFmPopNonce] = useState(0);
  // fmHome is the landing view: the Season home for a farmer, the map for an agent.
  const fmPageOpen = view !== fmHome || !!seasonPage || !!weatherField || !!prfField || !!jdDataFarmer || !!chopStep || (fmImmersive && fmSubOpen);
  const fmBackRef = useRef(null);
  fmBackRef.current = { overlay: !!weatherField || !!prfField || !!jdDataFarmer, seasonPage: !!seasonPage, view, chopStep, chopSent: !!chopSent, sub: fmImmersive && fmSubOpen };
  useEffect(() => {
    if (!isMobile || typeof window === "undefined") return;
    if (fmPageOpen && !fmPushedRef.current) {
      try { window.history.pushState({ fmPage: 1 }, ""); fmPushedRef.current = true; } catch (e) {}
    } else if (!fmPageOpen && fmPushedRef.current) {
      fmPushedRef.current = false;
      try { if (window.history.state && window.history.state.fmPage) window.history.back(); } catch (e) {}
    }
  }, [isMobile, fmPageOpen, fmPopNonce]);
  useEffect(() => {
    if (!isMobile || typeof window === "undefined") return;
    const onPop = () => {
      if (!fmPushedRef.current) return;
      fmPushedRef.current = false;
      const s = fmBackRef.current || {};
      if (s.overlay) { setWeatherField(null); setPrfField(null); setJdDataFarmer(null); }
      else if (s.seasonPage) setSeasonPage(null);
      else if (s.view !== fmHome) setView(fmHome);
      // The chop list's review steps back to picking (nothing is lost: the list was
      // saved on the way in); from picking, Back is the panel's own Cancel.
      else if (s.chopStep === "review" && !s.chopSent) { setChopStep("pick"); setChopMsg(null); }
      else if (s.chopStep) closeChop();
      else if (s.sub && fmSubCloseRef.current) fmSubCloseRef.current();
      setFmPopNonce((n) => n + 1);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isMobile]);   // eslint-disable-line
  // Leaving Field Mapper with a page open (a bottom-nav tap) left our pushed step in the
  // browser's history, so the next Back did nothing. Spend it on the way out.
  useEffect(() => () => {
    if (!fmPushedRef.current) return;
    fmPushedRef.current = false;
    try { if (window.history.state && window.history.state.fmPage) window.history.back(); } catch (e) {}
  }, []);
  // The chop planner's steps, built once and shown in one of two places: over the
  // desktop map, or in the phone's bottom sheet (FieldMapPanel, phone.chopPanel).
  // On a phone the chop picker used to drop the whole screen back to the DESKTOP
  // layout — the "Field Mapper" heading, the figures strip with its CLU/AIP line, the
  // tab row, a Filters menu listing AIPs, and a 420px map — in the busiest weeks.
  const chopPanelBody = !chopStep ? null : (
    <>

      {chopMsg && (
        <div style={{ marginBottom:10, padding:"8px 10px", borderRadius:7, fontSize:12.5, fontWeight:700,
          background: chopMsg.tone==="err" ? "#fdecec" : chopMsg.tone==="warn" ? "#fdf6e8" : "#eef7ea",
          color:      chopMsg.tone==="err" ? "#b00"    : chopMsg.tone==="warn" ? "#7a5a10" : "#2d6a1a" }}>
          {chopMsg.text}
        </div>
      )}

      {/* STEP 1 — picking on the map */}
      {chopStep === "pick" && (
        <>
          <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
            <div style={{ fontWeight:800, color:"#1a2e1a", fontSize:14 }}>Tap the fields you're chopping</div>
            <div style={{ fontSize:12, color:"#60735f" }}>{chopFarmerName}</div>
          </div>
          <div style={{ fontSize:12.5, color:"#54724f", marginTop:5, lineHeight:1.5 }}>
            Corn fields are outlined. Tap one to mark it for silage, tap again to unmark. Other crops are dimmed and can't be picked.
          </div>
          {chopSilageInsured.length > 0 && (
            <div style={{ marginTop:8, padding:"8px 10px", background:"#fdecec", borderLeft:"4px solid #c0392b",
                          borderRadius:5, fontSize:12.5, lineHeight:1.5, color:"#7a2e26" }}>
              <b>{chopToGrain.length} of your {chopSilageInsured.length} silage-insured field{chopSilageInsured.length===1?"":"s"} {chopToGrain.length===1?"is":"are"} not marked</b> ({fmtAc(chopToGrainAcres)} ac, outlined in red).
              Combining corn that&rsquo;s insured as silage has to be reported and appraised first, same as chopping grain corn.
              {chopToGrain.length > 0 && (
                <button onClick={selectAllSilageInsured}
                  style={{ display:"block", marginTop:7, padding:"6px 11px", border:"1px solid #c0392b",
                           borderRadius:6, background:"#fff", color:"#c0392b", fontWeight:800,
                           fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
                  Mark all {chopSilageInsured.length} silage-insured fields
                </button>
              )}
            </div>
          )}
          <div style={{ display:"flex", alignItems:"baseline", gap:14, margin:"11px 0 3px" }}>
            <div><span style={{ fontSize:22, fontWeight:800, color:"#ff8c1a" }}>{chopPicked.length}</span>
              <span style={{ fontSize:12, color:"#60735f", marginLeft:5 }}>field{chopPicked.length===1?"":"s"}</span></div>
            <div><span style={{ fontSize:22, fontWeight:800, color:"#1a2e1a" }}>{fmtAc(chopAcres)}</span>
              <span style={{ fontSize:12, color:"#60735f", marginLeft:5 }}>acres</span></div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:11, flexWrap:"wrap" }}>
            <button onClick={saveChopSelection} disabled={chopBusy}
              style={{ flex:1, minWidth:130, padding:"10px 14px", border:"none", borderRadius:8, background: chopBusy?"#9bb39b":"#2d6a1a",
                       color:"#fff", fontWeight:800, fontSize:13.5, cursor: chopBusy?"default":"pointer" }}>
              {/* Named for what it does. "Done" read as "finish and send",
                  which is the one thing it has never done. */}
              {chopBusy ? "Saving…" : "Save my list"}
            </button>
            <button onClick={closeChop} disabled={chopBusy}
              style={{ padding:"10px 14px", border:"1px solid #cdd9cd", borderRadius:8, background:"#fff",
                       color:"#54724f", fontWeight:700, fontSize:13, cursor:"pointer" }}>Cancel</button>
          </div>
        </>
      )}

      {/* STEP 2 — the summary, then send */}
      {chopStep === "review" && (
        <>
          <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
            <div style={{ fontWeight:800, color:"#1a2e1a", fontSize:14 }}>
              {chopSent ? "Sent to your agent" : "Your chopping list"}
            </div>
            <div style={{ fontSize:12, color:"#60735f" }}>{chopFarmerName} · {fieldYear || CURRENT_CROP_YEAR}</div>
          </div>

          {/* A sent plan is not a filed one. Re-sending files a NEW
              submission rather than editing this one, so the agent can see
              that it changed and when. */}
          {chopSent && (
            <div style={{ fontSize:12.5, color:"#54724f", marginTop:6, lineHeight:1.5 }}>
              Plans change &mdash; you can change this list any time and send it again. Your agent gets the updated one.
            </div>
          )}

          {/* SAVING AND SENDING ARE TWO DIFFERENT THINGS, AND THE SCREEN HAS
              TO SAY SO. The list is already written to the farm's record by
              the time this panel opens — the previous step's button does that
              — but the only way out of here was the send button, so a grower
              whose plan was not final had no move except to send it anyway or
              close the tab and wonder whether anything stuck. It is stated,
              and there is a way to leave that keeps the work. */}
          {!chopSent && (
            <div style={{ fontSize:12.5, color:"#54724f", marginTop:6, lineHeight:1.5 }}>
              <b style={{ color:"#2d6a1a" }}>This list is saved.</b> It stays on your account and you can come back
              and change it. Your agent has <b>not</b> been told yet &mdash; send it when the plan is final.
            </div>
          )}

          <div style={{ display:"flex", alignItems:"baseline", gap:16, margin:"10px 0 8px" }}>
            <div><span style={{ fontSize:24, fontWeight:800, color:"#ff8c1a" }}>{chopPicked.length}</span>
              <span style={{ fontSize:12, color:"#60735f", marginLeft:5 }}>field{chopPicked.length===1?"":"s"}</span></div>
            <div><span style={{ fontSize:24, fontWeight:800, color:"#1a2e1a" }}>{fmtAc(chopAcres)}</span>
              <span style={{ fontSize:12, color:"#60735f", marginLeft:5 }}>acres to chop</span></div>
          </div>

          {/* A grain-insured field being chopped is a CHANGE OF INTENT: it has to
              reach the AIP and be appraised BEFORE the crop is chopped. Stated as
              a fact about the acres, not as advice about what to do. */}
          {chopToSilage.length > 0 && (
            <div style={{ margin:"4px 0 10px", padding:"9px 11px", borderLeft:"4px solid #a9791f",
                          background:"#fdf6e8", borderRadius:5, fontSize:12.5, lineHeight:1.5, color:"#6a5a3a" }}>
              <b style={{ color:"#7a5a10" }}>{chopToSilage.length} of these {chopToSilage.length===1?"field is":"fields are"} insured as grain.</b>{" "}
              Chopping grain-insured corn has to be reported to the insurance company and appraised before it's chopped — your agent is being told with this list.
            </div>
          )}

          {/* THE OTHER DIRECTION, and the one that hides. A silage-insured field
              they are NOT chopping gets combined, which is equally a change of
              intent — but nothing happens on the field to prompt the call. */}
          {chopToGrain.length > 0 && (
            <div style={{ margin:"4px 0 10px", padding:"9px 11px", borderLeft:"4px solid #c0392b",
                          background:"#fdecec", borderRadius:5, fontSize:12.5, lineHeight:1.5, color:"#7a2e26" }}>
              <b style={{ color:"#c0392b" }}>{chopToGrain.length} field{chopToGrain.length===1?"":"s"} insured as silage {chopToGrain.length===1?"is":"are"} NOT on this list ({fmtAc(chopToGrainAcres)} ac).</b>{" "}
              If you combine those for grain instead, that has to be reported and appraised before you take them — your agent is being told with this list.
            </div>
          )}

          {chopPicked.length === 0 ? (
            <div style={{ fontSize:12.5, color:"#60735f", padding:"8px 0" }}>
              No fields marked. Everything on this farm stays grain.
            </div>
          ) : (
            <div style={{ maxHeight:190, overflowY:"auto", border:"1px solid #e6ede6", borderRadius:7 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
                <thead><tr style={{ background:"#f2f6f2", position:"sticky", top:0 }}>
                  <th style={{ textAlign:"left", padding:"6px 8px", color:"#54724f" }}>Farm</th>
                  <th style={{ textAlign:"left", padding:"6px 8px", color:"#54724f" }}>Tract</th>
                  <th style={{ textAlign:"left", padding:"6px 8px", color:"#54724f" }}>Field</th>
                  <th style={{ textAlign:"right", padding:"6px 8px", color:"#54724f" }}>Acres</th>
                </tr></thead>
                <tbody>
                  {chopPicked.map(f => (
                    <tr key={f.clu_id} style={{ borderTop:"1px solid #eef2ee" }}>
                      <td style={{ padding:"5px 8px", color:"#1a2e1a", fontWeight:700 }}>{f.fsn || "—"}</td>
                      <td style={{ padding:"5px 8px", color:"#54724f" }}>{f.tract || "—"}</td>
                      <td style={{ padding:"5px 8px", color:"#54724f" }}>{f.field || "—"}</td>
                      <td style={{ padding:"5px 8px", textAlign:"right", color:"#1a2e1a" }}>
                        {f.acres != null ? Number(f.acres).toFixed(1) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display:"flex", gap:8, marginTop:12, flexWrap:"wrap" }}>
            {!chopSent && (
              <>
                <button onClick={() => { setChopStep("pick"); setChopMsg(null); }} disabled={chopBusy}
                  style={{ padding:"10px 13px", border:"1px solid #cdd9cd", borderRadius:8, background:"#fff",
                           color:"#2d4a2d", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  ← Add or remove fields
                </button>
                {/* The way out that is not "send it". Nothing to write — the
                    previous step already saved — so this just closes. */}
                <button onClick={closeChop} disabled={chopBusy}
                  style={{ padding:"10px 13px", border:"1px solid #cdd9cd", borderRadius:8, background:"#fff",
                           color:"#54724f", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  Not final yet — I'll send later
                </button>
                <button onClick={sendChopPlan} disabled={chopBusy}
                  style={{ flex:1, minWidth:170, padding:"10px 14px", border:"none", borderRadius:8,
                           background: chopBusy?"#9bb39b":"#2d6a1a", color:"#fff", fontWeight:800, fontSize:13.5,
                           cursor: chopBusy?"default":"pointer" }}>
                  {chopBusy ? "Sending…" : "All set — email this to my agent"}
                </button>
              </>
            )}
            {chopSent && (
              <>
                <button onClick={() => { setChopSent(null); setChopMsg(null); setChopStep("pick"); }}
                  style={{ flex:1, minWidth:150, padding:"10px 14px", border:"1px solid #cdd9cd", borderRadius:8,
                           background:"#fff", color:"#2d4a2d", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  &larr; Change my chopping list
                </button>
                <button onClick={closeChop}
                  style={{ padding:"10px 14px", border:"none", borderRadius:8, background:"#2d4a2d",
                           color:"#fff", fontWeight:800, fontSize:13.5, cursor:"pointer" }}>Close</button>
              </>
            )}
          </div>

          {/* Corn the maturity model knows about that no polygon can reach. Named
              rather than dropped — a plan that is short and does not say so is
              the failure this repo keeps writing down. */}
          {chopSent && chopSent.unmapped_maturity_fields > 0 && (
            <div style={{ marginTop:10, fontSize:12, color:"#7a5a10", background:"#fdf6e8",
                          padding:"8px 10px", borderRadius:6, lineHeight:1.5 }}>
              {chopSent.unmapped_maturity_fields} more corn field{chopSent.unmapped_maturity_fields===1?"":"s"} on your farm
              {chopSent.unmapped_maturity_fields===1?" is":" are"} not drawn on this map yet, so {chopSent.unmapped_maturity_fields===1?"it wasn't":"they weren't"} included.
              Your agent has been told. Call the office if any of those need chopping too.
            </div>
          )}
        </>
      )}
    </>
  );
  const fmPhone = !fmImmersive ? null : (() => {
    const fmView = (k, label, badge = 0) => ({ k, label, badge, onClick: () => setView(k) });
    const farmCrops = [...new Set(allRows.filter(r => !selectionActive || r.farmer_id === selectedFarmerId).map(r => r.crop).filter(Boolean))].sort();
    const showPicker = showFarmPicker;
    // Every filter that narrows the map, crop chips included: a crop picked in the field
    // list and left on is otherwise invisible from the map. (The search sits in its own
    // box right above, so it is not counted twice.)
    const fmMapFilters = fmNonCrop + cropSet.size;
    return {
      year: fmYear,
      yearLabel: `${fmYear} fields`,
      title: fmWhoseFields,
      emptyText: `No ${fmYear} fields${selectionActive && selectedFarmerName ? ` for ${selectedFarmerName}` : ""} match. Clear the search or a filter to see more.`,
      crops: farmCrops,
      cropSet,
      onToggleCrop: (c) => setCropSet(prev => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; }),
      onClearCrops: () => setCropSet(new Set()),
      // Back-button plumbing (see SYSTEM BACK above) and the chop planner's panel.
      onSubChange: setFmSubOpen,
      subCloseRef: fmSubCloseRef,
      chopPanel: chopStep ? chopPanelBody : null,
      views: isPortal
        ? [fmView("verify", "My acres"), fmView("rainfall", "Weather"), fmView("history", "History"), fmView("machine", "Machine data")]
        : [fmView("season", "Season"), fmView("tree", "Acreage"), fmView("rainfall", "Weather"), fmView("history", "History"),
           fmView("missing", "Boundaries", geomClus && missing.length ? missing.length : 0), fmView("machine", "Machine data")],
      extra: chopFarmerId && chopUniverse.size > 0 ? (
        <button onClick={openChopPicker} disabled={chopBusy}
          style={{ width: "100%", height: 46, marginTop: 8, borderRadius: 12, border: "1px solid #e6d4a8", background: "#fdf6e8", color: "#7a5a10",
                   fontFamily: "inherit", fontSize: 13.5, fontWeight: 800, cursor: chopBusy ? "default" : "pointer" }}>
          {chopBusy ? "Loading…" : "Mark fields for chopping"}
        </button>
      ) : null,
      top: (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* The map is a page off the farmer's Season home, and this is the way back. */}
          {isPortal && (
            <button onClick={() => setView("season")} aria-label="Back to Field Mapper"
              style={{ alignSelf: "flex-start", pointerEvents: "auto", height: 40, padding: "0 16px 0 12px", borderRadius: 20, border: "none", background: "#fff",
                       color: T.green, fontFamily: "inherit", fontSize: 14, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 18px rgba(0,0,0,.28)",
                       display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1, marginTop: -2 }}>‹</span>Field Mapper
            </button>
          )}
          <div style={{ display: "flex", gap: 8, pointerEvents: "auto" }}>
            <label style={{ flex: 1, minWidth: 0, height: 46, background: "#fff", borderRadius: 23, display: "flex", alignItems: "center", gap: 9,
                            padding: q ? "0 7px 0 15px" : "0 15px", boxShadow: "0 6px 18px rgba(0,0,0,.28)", boxSizing: "border-box" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60735f" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
              <input value={q} onChange={e => setQ(e.target.value)} aria-label="Search fields"
                placeholder={isPortal ? "Search fields, farm number, tract" : "Search farm, FSN, tract, field"}
                style={{ border: "none", outline: "none", fontFamily: "inherit", fontSize: 16, color: T.ink, width: "100%", minWidth: 0, background: "transparent", padding: 0, boxSizing: "border-box" }} />
              {q && (
                <button type="button" aria-label="Clear search" onClick={(e) => { e.preventDefault(); setQ(""); }}
                  style={{ width: 32, height: 32, borderRadius: 16, border: "none", background: "#eef3ee", color: "#2d4a2d", fontSize: 13, cursor: "pointer", flexShrink: 0, fontFamily: "inherit" }}>✕</button>
              )}
            </label>
            {(fieldYears || []).length > 0 && (
              <select value={fieldYear ?? ""} onChange={e => setFieldYear(Number(e.target.value))} aria-label="Crop year"
                style={{ height: 46, padding: "0 14px", borderRadius: 23, border: "none", fontFamily: "inherit", fontSize: 15, fontWeight: 800,
                         background: offCurrentYear ? "#fdf6e3" : T.ink, color: offCurrentYear ? "#8a5a00" : "#a8d878",
                         boxShadow: "0 6px 18px rgba(0,0,0,.28)", WebkitAppearance: "none", appearance: "none", cursor: "pointer" }}>
                {(fieldYears || []).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
          </div>
          {(showPicker || fmMapFilters > 0) && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {showPicker && (
                <div style={{ pointerEvents: "auto" }}>
                  <FarmPicker big groups={pickerGroups} selectedName={selectedFarmerName} jdDataSet={jdDataSet}
                    selectedId={shownFarmId}
                    onPick={(id) => pickFarm(id)} onClear={() => pickFarm(null)} />
                </div>
              )}
              {fmMapFilters > 0 && (
                <button onClick={() => { setAipSet(new Set()); setStateSet(new Set()); setCountySet(new Set()); setTypeSet(new Set()); setCropSet(new Set()); }}
                  style={{ pointerEvents: "auto", height: 40, padding: "0 14px", borderRadius: 999, border: "none", background: "#fdf6e3", color: "#8a5a00",
                           fontFamily: "inherit", fontSize: 13, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 18px rgba(0,0,0,.28)" }}>
                  {fmMapFilters} filter{fmMapFilters === 1 ? "" : "s"} on · Clear
                </button>
              )}
            </div>
          )}
        </div>
      ),
    };
  })();
  const overlayBody = { flex:1, minHeight:0, overflowY:"auto", WebkitOverflowScrolling:"touch", padding: isMobile ? "12px" : "18px 20px" };

  return (
    <div style={{ maxWidth:1280, margin:"0 auto" }}>
      {weatherField && createPortal(
        <div style={fieldOverlay}>
          {isMobile && <FmPageHeader backLabel="Field Mapper" onBack={()=>backToMapper()} title="Weather" safeTop
            sub={[fmFieldTitle(weatherField), weatherField.farmer, weatherField.county ? fmTitleCase(weatherField.county) + " County" : null].filter(Boolean).join(" · ")} />}
          <div style={overlayBody}>
            <div style={{ maxWidth:1280, margin:"0 auto" }}>
              <FieldWeatherView field={weatherField} isMobile={isMobile} onBack={()=>backToMapper()} hideBack={isMobile} portal={isPortal} />
            </div>
          </div>
        </div>,
        document.body)}
      {prfField && createPortal(
        <div style={fieldOverlay}>
          {isMobile && <FmPageHeader backLabel="Field Mapper" onBack={()=>backToMapper()} title="PRF rainfall" safeTop
            sub={[fmFieldTitle(prfField), prfField.farmer, prfField.county ? fmTitleCase(prfField.county) + " County" : null].filter(Boolean).join(" · ")} />}
          <div style={overlayBody}>
            <div style={{ maxWidth:1280, margin:"0 auto" }}>
              <FieldPRFRainfallView field={prfField} isMobile={isMobile} onBack={()=>backToMapper()} mode={mode} hideBack={isMobile} cropYear={fieldYear || CURRENT_CROP_YEAR} />
            </div>
          </div>
        </div>,
        document.body)}
      {importOpen && <GeoImportModal onClose={()=>setImportOpen(false)} onImported={()=>setGeomNonce(n=>n+1)}
        geomSet={geomClus || new Set()} fieldByClu={fieldByClu} fieldByFTF={fieldByFTF} ftfByLoose={ftfByLoose} knownPolicies={knownPolicies || new Set()} isMobile={isMobile} />}
      {nameImportOpen && <FieldNameImportModal onClose={()=>setNameImportOpen(false)} onImported={()=>setLabelNonce(n=>n+1)}
        allRows={allRows} labels={labels} isMobile={isMobile} />}
      {/* Agent-only, and gated at the RENDER rather than only at the buttons that
          open it. JdConnectionsView lists every connected John Deere org and fires
          jd-adopt / jd-sync / jd-precompute / jd-county-backfill — admin mutations
          that a portal token gets 403 on but an admin PREVIEWING the portal does
          not. Both triggers are already !isPortal; this makes it unreachable
          rather than merely un-clicked. */}
      {!isPortal && jdConnOpen && <JdConnectionsView farmers={farmers} onClose={()=>setJdConnOpen(false)} />}
      {/* From a tapped field, open on THAT field's map (Kevin, 2026-10-31: "I was hoping it would
          take me to machine data maps"); from the farm-level button, the farm. */}
      {jdDataFarmer && <JdFieldDataView farmer={jdDataFarmer} farmerId={jdDataFarmer.id} onClose={()=>setJdDataFarmer(null)} portal={isPortal}
        initialFieldId={jdDataFarmer.jdFieldId || null} initialFieldName={jdDataFarmer.fieldName || null}
        initialYear={jdDataFarmer.year || fieldYear || CURRENT_CROP_YEAR} initialCrop={jdDataFarmer.crop || null} backLabel={jdDataFarmer.backLabel || null}
        initialView={jdDataFarmer.view || null} initialMetric={jdDataFarmer.metric || null} />}
      {/* The Season home draws its own header — it IS the landing page for a farmer. */}
      {fmPage && view !== "season" && (
        <FmPageHeader backLabel="Field Mapper" onBack={() => setView(fmHome)} title={FM_PAGE_TITLES[view] || "Field Mapper"}
          sub={`${fmWhoseFields} · ${fmYear}`}>
          {/* The phone picker: 40px pill, 16px search box, no autofocus — the small one
              focused a 12.5px box on open and iOS zoomed the page and raised the keyboard. */}
          {showFarmPicker && (
            <FarmPicker big groups={pickerGroups} selectedName={selectedFarmerName} jdDataSet={jdDataSet}
              selectedId={view === "machine" && machineFarmerId ? machineFarmerId : shownFarmId}
              onPick={(id) => pickFarm(id)} onClear={() => pickFarm(null)} />
          )}
          {(fieldYears || []).length > 1 && view !== "machine" && (
            <select value={fieldYear ?? ""} onChange={e => setFieldYear(Number(e.target.value))} aria-label="Crop year"
              style={{ height: 40, padding: "0 14px", borderRadius: 20, border: "1px solid #cdd9cd", fontFamily: "inherit", fontSize: 16, fontWeight: 800,
                       background: "#fff", color: T.ink, cursor: "pointer" }}>
              {(fieldYears || []).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          {/* ⚠ THE MAP'S SEARCH FOLLOWS YOU HERE, SO IT IS SHOWN HERE. Type "6949" on the
              map, open My acres, and "What we have you insured for" totalled only the
              matching fields — with no search box on this page and a filter count that
              left the search out. Machine data is not narrowed by it, so not there. */}
          {view !== "machine" && q.trim() && (
            <button onClick={() => setQ("")} aria-label={`Clear the search for ${q.trim()}`}
              style={{ height: 40, padding: "0 12px 0 14px", borderRadius: 20, border: "1px solid #cdd9cd", background: "#fff", color: T.ink,
                       fontFamily: "inherit", fontSize: 13, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, maxWidth: "100%" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Search: {q.trim()}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2d4a2d" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          )}
          {(() => {
            const n = fmNonCrop + cropSet.size + (view !== "machine" && q.trim() ? 1 : 0);
            return n > 0 && (
              <button onClick={() => { setAipSet(new Set()); setStateSet(new Set()); setCountySet(new Set()); setTypeSet(new Set()); setCropSet(new Set()); setQ(""); }}
                style={{ height: 40, padding: "0 14px", borderRadius: 20, border: "none", background: "#fdf6e3", color: "#8a5a00",
                         fontFamily: "inherit", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                {n} filter{n === 1 ? "" : "s"} on · Clear
              </button>
            );
          })()}
        </FmPageHeader>
      )}
      {!fmImmersive && !fmPage && <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:6 }}>
        <h1 style={{ margin:0, color:T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>Field Mapper</h1>
        <div style={{ fontSize:12, color:"#60735f" }}>
          {/* The boundaries shipped; this line still promised them. A page that
              says a feature is "coming" while drawing it reads as an app nobody
              maintains, and it is the first thing a customer sees on this screen. */}
          {/* A customer reads plain language; "M13/NAU", "CLU" and "AIP" are our
              file names and acronyms, not theirs. Same facts. */}
          {isPortal
            ? "Your fields as reported to your insurance company, with the field boundaries where they are on file"
            : "Every reported field in the book, with its boundary where the AIP has one"}
        </div>
      </div>}

      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:"#60735f" }}><UiSpinner />Loading field inventory…</div>
      ) : err ? (
        <div style={{ ...card, color:"#b00", borderColor:"#e0bcbc" }}>Couldn’t load fields: {err}</div>
      ) : allRows.length === 0 ? (
        <div style={{ ...card, color:"#54724f", lineHeight:1.6 }}>
          <div style={{ fontWeight:800, color:"#1a2e1a", marginBottom:4 }}>{isPortal ? "No fields on file yet." : "No fields yet."}</div>
          {/* A grower — often a dairy or livestock customer with no crop acres at all —
              read our file names here ("M13 / NAU sync (P27 records)"). Same fact, in
              the words My acres already uses for it. */}
          {isPortal ? (
            <>Your acres appear here once your acreage report is processed — usually after the
              mid-summer reporting deadline.</>
          ) : (
            <>The field inventory populates from the daily M13 / NAU sync (P27 records), which carry CLU / FSN / field / crop / acres.
              Field rows appear after a sync runs and acreage is reported — most spring fields land after the mid-summer acreage-reporting deadline.</>
          )}
        </div>
      ) : (
        <>
          {/* Inventory summary. Was six full-height KPI cards pushing the map below
              the fold; these are the same numbers as one quiet strip, so the map
              starts higher on every screen. */}
          {!fmImmersive && !fmPage && <div style={{ ...uiCard(isMobile ? 10 : 12), display: "flex", flexWrap: "wrap",
                        gap: isMobile ? 14 : 26, alignItems: "baseline", margin: "10px 0 12px" }}>
            {[
              ["Fields",    scopedRows.length.toLocaleString(), null],
              // "Mapped acres", not "Acres": this total mixes AIP-reported acreage
              // with CLU-boundary measurements, and the bare word reads as the
              // insured figure. Authoritative insured acres are in the Insurance
              // Summary below, which is sourced entirely from crop_coverage.
              ["Mapped acres", fmtAc(totalAcres),             acreProvenanceNote],
              // "1 FARMERS" on a customer's own page says nothing; a multi-operation
              // login still sees its count.
              ...(isPortal && kFarmers === 1 ? [] : [["Farmers",   kFarmers.toLocaleString(),        null]]),
              ["Counties",  kCounties.toLocaleString(),       null],
              ["Crops",     kCrops.toLocaleString(),          null],
            ].map(([label, value, sub]) => (
              // A provenance note is not a footnote. It sits on its own line under
              // the figure it qualifies rather than inline in parentheses, and it
              // takes the amber this app already uses for prevent plant when the
              // WHOLE figure is a boundary measurement — the one case that must not
              // read as authoritative insured acreage.
              <div key={label} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
                  <span style={{ ...uiNum, fontSize: isMobile ? 16 : 19, fontWeight: 800, color: T.ink }}>{value}</span>
                  <span style={{ ...uiLabel, fontSize: 10 }}>{label}</span>
                </div>
                {sub && !isMobile && (
                  <span style={{ fontSize: 10.5, lineHeight: 1.35, maxWidth: 340,
                                 fontWeight: acreProvenance.reported <= 0 && acreProvenance.boundary > 0 ? 700 : 400,
                                 color: acreProvenance.reported <= 0 && acreProvenance.boundary > 0 ? "#9a5a1a" : T.faint }}>
                    {sub}
                  </span>
                )}
              </div>
            ))}
            {/* On a phone the figures read as ONE line and the provenance note sits
                full-width beneath them — under the number it qualifies, not wedged
                into a 240px column that pushed every other figure onto its own row
                and the map half a screen down. Same note, same colours. */}
            {isMobile && acreProvenanceNote && (
              <span style={{ flexBasis: "100%", fontSize: 10.5, lineHeight: 1.35, marginTop: -6,
                             fontWeight: acreProvenance.reported <= 0 && acreProvenance.boundary > 0 ? 700 : 400,
                             color: acreProvenance.reported <= 0 && acreProvenance.boundary > 0 ? "#9a5a1a" : T.faint }}>
                {acreProvenanceNote}
              </span>
            )}
          </div>}

          {/* ── Toolbar ──────────────────────────────────────────────────────
              Two rows instead of one 15-control pile-up:
                1. view tabs (what you're looking at) — the primary decision
                2. search + Filters popover + Actions menu
              Filters and Actions are collapsed by default and both carry their
              state on the button (an active-filter count, a missing-boundary
              count), so nothing is hidden — just not shouting. `zIndex` stays
              high because the multi-selects render over the Leaflet map. */}
          {!fmImmersive && !fmPage && (() => {
            const activeFilters = (q ? 1 : 0) + aipSet.size + stateSet.size + countySet.size + cropSet.size + typeSet.size;
            const clearAll = () => { setQ(""); setAipSet(new Set()); setStateSet(new Set()); setCountySet(new Set()); setCropSet(new Set()); setTypeSet(new Set()); };
            const VIEWS = [
              // Portal only, and first — "check your acres" is the reason a grower
              // opens this at all. Agents don't need it; they have Acreage + the
              // Machine-vs-FSA audit.
              ...(isPortal ? [{ k: "season", label: "My season" }, { k: "verify", label: "My acres" }] : []),
              { k: "map",      label: "Map" },
              ...(isPortal ? [] : [{ k: "season", label: "Season", title: "The selected farm's season, as the farmer sees it" }, { k: "tree", label: "Acreage" }]),
              { k: "rainfall", label: "Weather" },
              { k: "history",  label: "History" },
              ...(isPortal ? [] : [{ k: "missing", label: "Boundaries", badge: geomClus && missing.length ? missing.length : 0,
                title: "Insured fields we have no shapefile/boundary for — the worklist to source boundaries" }]),
              // Sampling history, what's due, and the chain that runs off it:
              // import → recommendation → prescription. PARKED 2026-08-24 — see
              // SOIL_SAMPLES_PARKED. The `!isPortal` beside it is the OLD reason
              // and is now redundant; it stays so that un-parking restores the
              // agent-only posture rather than silently shipping the tab to
              // growers, which is a separate product decision.
              ...(isPortal || SOIL_SAMPLES_PARKED ? [] : [{ k: "soil", label: "Soil samples",
                title: "Sampling history, what needs pulling, recommendations and prescriptions" }]),
              // The grower's own machine records. This was off in the portal
              // because the tab was built as an AGENT tool — a picker over the
              // whole customer list, the Connected-Accounts overlay, and a claim
              // read — and because jdDataSet was never fetched for a portal user,
              // so it could only ever have said "not connected". None of that was
              // true of the DATA: every endpoint under it is caller-scoped. So the
              // portal gets the same records with the agency furniture removed —
              // no roster, no invite overlay, no Harvest Watch (see JdFieldDataView).
              { k: "machine", label: "Machine data", title: isPortal
                  ? "Your John Deere planting, harvest and yield maps"
                  : "John Deere planting/harvest, yield maps, and claim attribution for the selected farm" },
            ];
            const tabBtn = (v) => {
              const on = view === v.k;
              const warn = v.k === "missing" && v.badge > 0;
              return (
                <button key={v.k} onClick={() => setView(v.k)} title={v.title}
                  style={{ border: "none", borderRadius: T.r.sm, padding: isMobile ? "7px 11px" : "8px 16px",
                           fontSize: isMobile ? 12 : 12.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
                           display: "flex", alignItems: "center", gap: 6,
                           background: on ? T.ink : "transparent",
                           color: on ? "#a8d878" : warn ? T.warn : T.inkSoft, fontFamily: "inherit" }}>
                  {v.label}
                  {v.badge > 0 && (
                    <span style={{ ...uiNum, fontSize: 10.5, fontWeight: 800, padding: "1px 6px", borderRadius: 9,
                                   background: on ? "#2d4a2d" : "#fdf0e3", color: on ? "#a8d878" : T.warn }}>{v.badge}</span>
                  )}
                </button>
              );
            };

            return (
              <div style={{ position: "relative", zIndex: 1200, marginBottom: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {/* ⚠ WRAPPED, NOT SWIPED — the same rule every other tab strip here
                    follows. Measured on production 2026-09-24 at a 390px viewport:
                    "Machine data" ended at 548px, so the grower's own planting and
                    yield maps sat off the right edge behind a swipe with nothing on
                    screen to say they were there. `overflowX: auto` kept the page
                    still and hid the feature; wrapping does neither. */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, background: "#eef3ee", borderRadius: T.r.md, padding: 3,
                              alignSelf: isMobile ? "stretch" : "flex-start" }}>
                  {VIEWS.map(tabBtn)}
                </div>

                <div style={{ ...uiCard(isMobile ? 10 : 12), display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  {/* ONE FARM SELECTOR FOR THE WHOLE SCREEN, on every tab (2026-08-13).
                      It used to live inside the map's chip row, which meant Acreage,
                      Weather, History and Boundaries had no way to change farms at all —
                      only a "Viewing: X" chip telling you which one you were stuck on —
                      while the map had this picker AND a checkbox rail doing the same job
                      through a second piece of state. Worse, the map was handed a `groups`
                      list already narrowed to the selected farm, so the picker could not
                      switch farms: going Kludt → Anchor meant "All farms" first. It reads
                      the full list here and writes the one selection everything else reads. */}
                  {/* A one-farm customer has nothing to pick. */}
                  {showFarmPicker && <FarmPicker groups={pickerGroups} selectedName={selectedFarmerName} jdDataSet={jdDataSet}
                    /* ⚠ THE PICKER SHOWS WHAT THE SCREEN IS SHOWING, ON EVERY TAB.
                       It read `selectedFarmerId` everywhere but the machine tab, so a
                       search that left ONE farm on the map still said "All farms" — which
                       is what made the machine tab's "pick a farm" read as a bug rather
                       than as a question. Resolved farm first; it falls back to the sticky
                       selection, so a picked farm still wins. Off the machine tab it is
                       never the machine tab's own default (the first farm WITH machine
                       data) — that is a choice for one tab, not what the map shows. */
                    selectedId={view === "machine" ? (machineFarmerId || selectedFarmerId) : shownFarmId}
                    onPick={(id) => pickFarm(id)} onClear={() => pickFarm(null)} />}

                  {/* CROP YEAR — which year's field map is drawn. Defaults to the
                      current book. It goes AMBER off that year on purpose: a prior
                      year's map is genuinely patchier (CY2025 is 9.0% un-mapped
                      against CY2026's 1.9%), and the only way to misread this screen
                      is to not know which year you are looking at. That is exactly
                      how 2026-08-13's "all our boundaries disappeared" happened —
                      the backfill wrote CY2025 and the map, then year-blind, drew
                      both. Listed years are PROBED, so every option has a map. */}
                  {(fieldYears || []).length > 0 && (
                    <select value={fieldYear ?? ""} onChange={e => setFieldYear(Number(e.target.value))}
                      title={offCurrentYear
                        ? `Showing the ${fieldYear} field map — not the current ${CURRENT_CROP_YEAR} book`
                        : "Which crop year's field map to draw"}
                      style={{ ...sel, cursor: "pointer", fontWeight: 700,
                               color: offCurrentYear ? "#8a5a00" : undefined,
                               background: offCurrentYear ? "#fdf6e3" : undefined,
                               borderColor: offCurrentYear ? "#e0c98a" : undefined }}>
                      {(fieldYears || []).map(y => (
                        <option key={y} value={y}>{y} fields{y === CURRENT_CROP_YEAR ? " · current" : ""}</option>
                      ))}
                    </select>
                  )}

                  {/* Machine Data is the grower's John Deere records, not the FSA field
                      inventory this box searches, so it filters nothing you can see there.
                      The picker above is the control that tab actually uses. */}
                  {view !== "machine" && (
                    <input value={q} onChange={e => setQ(e.target.value)} placeholder={isPortal ? "Search fields, farm number, tract…" : "Search farmer, policy, FSN, tract, field…"}
                      style={{ ...sel, flex: isMobile ? "1 1 100%" : "1 1 260px", minWidth: 160 }} />
                  )}

                  <button onClick={() => { setFiltersOpen(o => !o); setActionsOpen(false); }}
                    style={{ ...sel, cursor: "pointer", fontWeight: 700, display: "flex", alignItems: "center", gap: 7,
                             color: activeFilters ? "#fff" : T.inkSoft,
                             background: activeFilters ? T.inkSoft : undefined,
                             borderColor: activeFilters ? T.inkSoft : undefined }}>
                    Filters
                    {activeFilters > 0 && (
                      <span style={{ ...uiNum, fontSize: 10.5, fontWeight: 800, padding: "1px 6px", borderRadius: 9,
                                     background: "rgba(255,255,255,.25)" }}>{activeFilters}</span>
                    )}
                    <span style={{ fontSize: 10 }}>{filtersOpen ? "▲" : "▼"}</span>
                  </button>

                  {activeFilters > 0 && (
                    <button onClick={clearAll} style={{ ...sel, cursor: "pointer", color: T.green, fontWeight: 700, border: "none", background: "none" }}>Clear</button>
                  )}

                  {/* CORN TYPE, promoted out of the Filters popover. "Show me the silage
                      ground" is a question asked often enough that it shouldn't cost two
                      clicks and a scan of five other dropdowns. Only rendered when the view
                      actually holds more than one corn type, so a grain-only book sees
                      nothing. Untyped corn is its own chip — it is not grain. */}
                  {/* Not on the Soil Samples tab: a corn grain-vs-silage split says nothing
                      about what a field's chemistry is or when it was last pulled. */}
                  {types.length > 1 && view !== "soil" && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#eef3ee",
                                   borderRadius: T.r.md, padding: 3 }}>
                      {types.map(t => {
                        const on = typeSet.has(t);
                        const untyped = t === CORN_TYPE_UNTYPED;
                        return (
                          <button key={t} onClick={() => setTypeSet(prev => {
                            const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; })}
                            title={untyped
                              ? "Corn the AIP file carries no type on — grain vs silage is not reported. Not counted as grain."
                              : `Show only ${t} corn`}
                            style={{ cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700,
                                     padding: "4px 10px", borderRadius: T.r.sm, whiteSpace: "nowrap",
                                     border: "1px solid " + (on ? "#bcdcb4" : "transparent"),
                                     background: on ? "#fff" : "transparent",
                                     color: on ? T.ink : (untyped ? "#9a8f5a" : T.inkSoft),
                                     display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <span style={{ width: 9, height: 9, borderRadius: 2, display: "inline-block", flexShrink: 0,
                                           background: fieldCropColor("CORN",
                                             t === "Silage (GSS)" ? "026" : untyped ? "" : "016") }} />
                            {untyped ? "Corn · type not reported" : t}
                          </button>
                        );
                      })}
                    </span>
                  )}

                  {/* Enter the chop picker. Shown to AGENTS as well as growers, not
                      just the portal: the agent is the one on the phone with the
                      grower, and a control only one of them can see is a control they
                      cannot talk about. It writes the same value either way — the
                      row's updated_by_role is what records who chose.

                      Hidden (not disabled) when there is no corn to pick or no single
                      farm in scope, because a dead-but-live-looking button is exactly
                      what made the maturity toggle read as broken in the portal. */}
                  {view === "map" && !chopStep && chopFarmerId && chopUniverse.size > 0 && (
                    <button onClick={openChopPicker} disabled={chopBusy}
                      title={`Pick the corn fields ${chopFarmerName} intends to chop, then send the list to the agent`}
                      style={{ ...sel, cursor: chopBusy ? "default" : "pointer", fontWeight: 800,
                               color: "#7a5a10", background: "#fdf6e8", borderColor: "#e6d4a8",
                               display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {chopBusy ? "Loading…" : "Mark fields for chopping"}
                    </button>
                  )}

                  {!isPortal && (
                    <div style={{ marginLeft: "auto", position: "relative" }}>
                      <button onClick={() => { setActionsOpen(o => !o); setFiltersOpen(false); }}
                        style={{ ...sel, cursor: "pointer", fontWeight: 700, color: T.ink, background: "#eef7e8", borderColor: "#bcdcb4" }}>
                        Actions <span style={{ fontSize: 10 }}>{actionsOpen ? "▲" : "▼"}</span>
                      </button>
                      {actionsOpen && (
                        <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 1300, minWidth: 236,
                                      background: T.surface, border: `1px solid ${T.line}`, borderRadius: T.r.md,
                                      boxShadow: "0 8px 24px -10px rgba(27,38,32,.3)", overflow: "hidden" }}>
                          {[
                            ["", "Export to Excel", "Export the current view to Excel", exportXlsx],
                            ["", "Import boundaries", "Import field boundaries from a shapefile / GeoJSON — only the ones we don't already have", () => setImportOpen(true)],
                            ["", "Import farmer boundaries", "Import a farmer's field boundaries (JD Operations Center / Climate FieldView export) — names matched to insured fields by location", () => setNameImportOpen(true)],
                            ["", "John Deere connections", "Which grower orgs are sharing data and which farmer each maps to", () => setJdConnOpen(true)],
                          ].map(([icon, label, title, fn]) => (
                            <button key={label} title={title} onClick={() => { setActionsOpen(false); fn(); }}
                              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                                       padding: "10px 14px", border: "none", borderBottom: `1px solid ${T.lineSoft}`,
                                       background: "none", cursor: "pointer", fontSize: 13, color: T.ink, fontFamily: "inherit" }}>
                              {icon ? <span style={{ width: 16 }}>{icon}</span> : null}{label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {filtersOpen && (
                    <div style={{ flexBasis: "100%", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
                                  borderTop: `1px solid ${T.lineSoft}`, paddingTop: 10, marginTop: 2 }}>
                      <FieldMultiSelect label="AIPs"     options={aipOpts}   selected={aipSet}    onChange={setAipSet} />
                      <FieldMultiSelect label="states"   options={stateOpts} selected={stateSet}  onChange={setStateSet} />
                      <FieldMultiSelect label="counties" options={counties}  selected={countySet} onChange={setCountySet} />
                      <FieldMultiSelect label="crops"    options={crops}     selected={cropSet}   onChange={setCropSet} />
                      {/* corn type lives in the always-visible chip row above, not here —
                          two controls over one filter state is how they drift out of step */}
                      <select value={groupSort} onChange={e => setGroupSort(e.target.value)} title="Order growers (respects the crop/county filters)"
                        style={{ ...sel, cursor: "pointer" }}>
                        <option value="name">Sort: Name A–Z</option>
                        <option value="acres_desc">Sort: Acres ↓</option>
                        <option value="acres_asc">Sort: Acres ↑</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* The "Viewing: X ✕" chip that used to sit here is gone (2026-08-13). It was a
              THIRD readout of one selection — the toolbar picker now names the farm and
              clears it, on every tab rather than four of them — and its "🚜 Field Data"
              button opened the same JdFieldDataView the 🚜 Machine Data tab renders for
              the same farm. The map's field popup still opens that view as an overlay,
              which is a different move: it keeps your place on the map. */}

          {/* NOTHING MATCHED — and it says so, naming the farm and the filters that
              emptied it. Every tab below draws from `scopedRows`, so without this the
              answer to "does this farm have silage?" is a blank map and an empty tree,
              which reads as broken. It is the visible half of the selectionActive fix:
              the page used to avoid the blank by quietly dropping the farm and drawing
              the whole book's silage instead. Machine Data is excluded — it renders the
              grower's John Deere records, not this FSA field inventory. */}
          {view !== "machine" && view !== "season" && !fmImmersive && scopedRows.length === 0 && (() => {
            const bits = [
              ...(typeSet.size   ? [[...typeSet].join(" or ")] : []),
              ...(cropSet.size   ? [[...cropSet].join(" or ")] : []),
              ...(countySet.size ? [[...countySet].join(" or ")] : []),
              ...(stateSet.size  ? [[...stateSet].map(f => FIELD_STATE_NM[f] || f).join(" or ")] : []),
              ...(aipSet.size    ? [[...aipSet].map(aipLabel).join(" or ")] : []),
              ...(q.trim()       ? [`“${q.trim()}”`] : []),
            ];
            return (
              <div style={{ ...card, color:"#54724f", lineHeight:1.6, marginBottom:8 }}>
                <div style={{ fontWeight:800, color:"#1a2e1a", marginBottom:4 }}>
                  No fields{selectionActive && selectedFarmerName ? ` for ${selectedFarmerName}` : ""} match{bits.length ? "" : " this view"}.
                </div>
                {bits.length > 0 && <>Nothing in the {fieldYear || CURRENT_CROP_YEAR} field map matches <b>{bits.join(" · ")}</b>. </>}
                {selectionActive
                  ? "Clear a filter, or pick another farm."
                  : "Clear a filter to widen the view."}
              </div>
            );
          })()}

          {/* MAP VIEW — scoped to the sticky farm when one is selected (deep-linked from a farmer card), else book-wide */}
          {/* MY ACRES — grower-facing verification. Portal only; the tab isn't offered to agents. */}
          {/* SEASON — the farmer's Field Mapper home (src/fieldmapper/FarmerSeasonHome.jsx).
              One farm at a time, the same farm Machine data opens on. */}
          {view === "season" && (() => {
            const sFarm = machineFarmerId;
            const sName = nameOf(allRows.find(r => r.farmer_id === sFarm) || {});
            return (
              <FarmerSeasonHome farm={sFarm ? { id: sFarm, name: sName } : null}
                farmPicker={showFarmPicker ? (
                  <FarmPicker big groups={pickerGroups} selectedName={selectedFarmerName} jdDataSet={jdDataSet} selectedId={sFarm || selectedFarmerId}
                    onPick={(id) => { setSeasonPage(null); pickFarm(id); }} onClear={() => { setSeasonPage(null); pickFarm(null); }} />
                ) : null}
                isPortal={isPortal} isMobile={isMobile} year={fmYear} years={fieldYears || []} onYear={(y) => setFieldYear(y)}
                policyRows={allRows} policyYear={fieldYear} hasMachine={jdDataSet ? !!(sFarm && jdDataSet.has(sFarm)) : null}
                authedFetch={authedFetch} page={seasonPage} onPage={setSeasonPage}
                onBack={isPortal ? null : () => { setSeasonPage(null); setView("map"); }}
                onOpenMap={() => { setSeasonPage(null); setView("map"); }}
                onOpenMachine={(o) => setJdDataFarmer({ id: sFarm, name: sName, jdFieldId: o.jdFieldId || null, fieldName: o.fieldName || null,
                  crop: o.crop || null, backLabel: o.backLabel || null, year: o.year || null, view: o.view || null, metric: o.metric || null })}
                ui={SEASON_UI} />
            );
          })()}

          {view === "verify" && (
            <FieldVerifyPanel rows={scopedRows} labels={labels} cluYield={cluYield}
              cty={cty} fmtAc={fmtAc} acreOf={acreOf} isMobile={isMobile} />
          )}

          {view === "map" && (() => { const fmPanel = (h) => <FieldMapPanel phone={fmPhone ? { ...fmPhone, height: h } : null} rows={scopedRows} groups={selectionActive ? groups.filter(g => g.id === selectedFarmerId) : groups} prices={prices} isMobile={isMobile} mode={mode} restrictFarmerIds={restrictFarmerIds} cluAcres={cluAcres} labels={labels} jdNames={jdNames} cluYield={cluYield} cluEvidence={cluEvidence} onSaveLabel={saveFieldLabel} onViewWeather={openWeather} onViewPRF={openPRF} onSelectFarm={(fid)=>pickFarm(fid || null)} selectedFarmerId={selectionActive ? selectedFarmerId : null} focus={mapFocus} onFocusDone={()=>setMapFocus(null)} onViewport={(v)=>{ mapViewportRef.current = v; }} initialView={selectionActive ? null : mapViewportRef.current}
            jdDataSet={jdDataSet} onOpenJdReports={(id, name, jdFieldId, fieldName, crop, backLabel) => setJdDataFarmer({ id, name: name || "Farm", jdFieldId: jdFieldId || null, fieldName: fieldName || null, crop: crop || null, backLabel: backLabel || null })} allClus={allClus}
            chopMode={chopStep === "pick"} chopSel={chopSel} onChopToggle={toggleChop} cropYear={fieldYear || CURRENT_CROP_YEAR} prfAcres={prfAcres} />;
            return <FmImmersive on={fmImmersive}>{fmPanel}</FmImmersive>; })()}

          {/* ── CHOP PLANNER OVERLAY ────────────────────────────────────────────
              Sits over the map while the grower picks. It is an overlay rather than
              a side panel on purpose: picking fields needs the map, and a panel that
              pushed the map narrower would make small fields harder to hit. */}
          {view === "map" && chopStep && !fmImmersive && (
            <div style={{ position:"relative" }}>
              <div style={{ position:"absolute", left:0, right:0, bottom:12, zIndex:1200, display:"flex", justifyContent:"center", pointerEvents:"none" }}>
                <div style={{ pointerEvents:"auto", background:"#fff", border:"1px solid #cdd9cd", borderRadius:12,
                              boxShadow:"0 6px 24px rgba(20,40,20,.22)", padding: isMobile?12:14,
                              width: isMobile ? "94%" : (chopStep === "review" ? 620 : 460), maxWidth:"96%",
                              maxHeight: isMobile ? "58vh" : "62vh", overflowY:"auto" }}>
                  {chopPanelBody}
                </div>
              </div>
            </div>
          )}


          {/* WEATHER VIEW — per-field high-def weather (rain + GDD), scoped to the sticky farm, sortable + exportable */}
          {/* cropYear: the year the map is drawn for, so the 2025 map shows the 2025
              season. onOpenField opens the field's own weather page over the tab. */}
          {view === "rainfall" && <FieldWeatherPanel rows={scopedRows} centroids={centroids} nameOf={nameOf} cty={cty} isMobile={isMobile} portal={isPortal}
            cropYear={fieldYear || CURRENT_CROP_YEAR}
            onOpenField={(r, name) => openWeather({ farmer: nameOf(r), farmer_id: r.farmer_id, crop: r.crop, type_code: r.type_code || null,
              fsn: r.fsn, tract: r.tract, field: r.field_id, field_id: r.field_id, county: cty(r), planted: r.report_date, label: name || null,
              clu_id: r.clu_id, latitude: r.latitude, longitude: r.longitude })}
            onWeatherPage={onNav ? () => onNav("rainwatch") : null}
            onLocate={(f)=>{ setMapFocus(f); setView("map"); }} onPickFarm={(id) => pickFarm(id)} />}

          {/* SOIL SAMPLES VIEW — sampling history and what's due, then the chain that
              runs off it. Scoped to the sticky farm; the map keeps the layer itself. */}
          {/* Gated at the RENDER, not just at the tab button. A portal session carries
              its nav state in sessionStorage, so a stale view="soil" would otherwise
              paint an agent surface for a farmer — and in admin "preview as" the token
              is still an admin's, so the fetch could never be the only guard. */}
          {view === "soil" && !isPortal && !SOIL_SAMPLES_PARKED && (
            <SoilSamplesView rows={scopedRows} labels={labels} isMobile={isMobile}
              farmerId={selectionActive ? selectedFarmerId : null}
              farmerName={(groups.find(g => g.id === selectedFarmerId) || {}).name}
              onLocate={(row, ev) => { setMapFocus({ clu: row.clu, lat: ev.centroid_lat, lng: ev.centroid_lon, name: row.name }); setView("map"); }} />
          )}

          {/* HISTORY VIEW — per-field crop rotation (field_crop_history), scoped to the sticky farm, sortable + exportable */}
          {view === "history" && <FieldHistoryPanel rows={scopedRows} nameOf={nameOf} cty={cty} isMobile={isMobile} cropYear={fieldYear || CURRENT_CROP_YEAR} portal={isPortal}
            onLocate={(f)=>{ setMapFocus(f); setView("map"); }} onPickFarm={(id) => pickFarm(id)} />}

          {/* MACHINE DATA VIEW — John Deere planting/harvest/yield + claim attribution for
              the selected farm, rendered inline (the Field Data view embedded as a tab). */}
          {view === "machine" && (() => {
            // A grower with ONE operation shouldn't have to pick it, and a login
            // covering several lands on the first that HAS records — see
            // `machineFarmerId` above, which is where that default is computed so the
            // toolbar picker can display it. (A GROWER WITH TWO POLICIES SAW NOTHING
            // before that default existed: Panek — Panek Farms + Alan Panek — landed
            // on "All farms", jdFarm stayed null, and the "No machine records yet"
            // panel rendered over 5,300 operations.)
            //
            // The farm picker that used to sit in this tab's own bar is gone: it is in
            // the screen toolbar now, above every tab, reading and writing the single
            // selection. Two pickers over one farm is what this whole pass removed.
            const jdFarm = machineFarmerId;
            const jdName = jdFarm === selectedFarmerId ? (selectedFarmerName || "Farm")
                         : (((groups.find(g => g.id === jdFarm) || groups[0]) || {}).name || "Farm");
            const hasJd = !!(jdFarm && jdDataSet && jdDataSet.has(jdFarm));
            // The agency furniture — the connections/invite overlay — built here rather
            // than inline, so the render of the records themselves carries no agent-only
            // guard above it. That matters beyond tidiness: check-portal decides a
            // component is agent-only from the guards near its render site, and an
            // inline `!isPortal` a few lines up made it skip JdFieldDataView entirely —
            // a component the portal DOES render, dropped from the audit.
            const agencyBar = !isPortal ? (
              <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                <button onClick={()=>setJdConnOpen(true)} title="Which grower orgs are connected + invite more"
                  style={{ border:"1px solid #cdd9cd", background:"#f6faf6", borderRadius:999, padding:"6px 14px", fontSize:12, fontWeight:700, color:"#2d4a2d", cursor:"pointer" }}>Connected accounts</button>
                {jdFarm && !hasJd &&
                  <span style={{ fontSize:12, color:"#8a5a00" }}>This farm isn't connected to John Deere yet.</span>}
              </div>
            ) : null;
            // Nothing to show. The grower gets the panel that can fix it — and
            // that knows "not connected" from "connected, nothing across yet".
            const empty = isPortal ? (
              <JdConnectPanel isMobile={isMobile} restrictFarmerIds={restrictFarmerIds} what="machine records"
                connectedTitle="No machine records yet"
                connectedNote="Your John Deere connection is live — planting and harvest maps appear here within about a day of the machines running." />
            ) : (
              <div style={{ padding:"40px 20px", textAlign:"center", color:"#60735f", fontSize:13.5, lineHeight:1.6, background:"#fff", border:"1px solid #e0e8e0", borderRadius:12 }}>
                {jdFarm
                  ? <>No machine data for this farm yet.<br/>Use <b>Connected accounts</b> to invite them to John Deere — or upload a yield map below, from any monitor.</>
                  : filteredFarms.length > 1 ? (
                      /* ⚠ AN AMBIGUOUS SEARCH NAMES THE FARMS AND OFFERS THEM.
                       *
                       * Kevin, 2026-10-06: "field mapper is clunky now and somewhat
                       * confusing." Typing a name that matches two farms left the map
                       * narrowed and this tab saying "pick a farm above" with no hint that
                       * TWO was the problem — a dead end that looks like a missing feature.
                       *
                       * It is the name-split class surfacing as navigation: PANEK is
                       * PANEK FARMS and PANEK, ALAN, two farmer rows one search cannot
                       * choose between. The comment above this block already records that
                       * exact case costing a grower 5,300 operations of machine data.
                       * Naming them and offering them is the whole fix — the agent knows
                       * which one they meant, the screen just never asked. */
                      <>
                        <div style={{ color:"#1a2e1a", fontWeight:700, marginBottom:4 }}>
                          {jdFarmsOnScreen.length
                            ? `${jdFarmsOnScreen.length} farm${jdFarmsOnScreen.length === 1 ? "" : "s"} on screen ${jdFarmsOnScreen.length === 1 ? "has" : "have"} machine data`
                            : (filteredFarms.length > FARM_CHOICE_CAP ? "None of the farms on screen have machine data yet" : "None of these farms have machine data yet")}
                        </div>
                        <div style={{ marginBottom:12 }}>
                          {jdFarmsOnScreen.length
                            ? "Machine data is per farm — pick which one."
                            : <>Use <b>Connected accounts</b> to invite a grower to John Deere, or upload a yield map below.</>}
                        </div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center", maxHeight: 300, overflowY:"auto", padding:2 }}>
                          {/* With data first; on a NARROW search the matches without data
                              stay visible but muted, so "which PANEK" is still answerable.
                              On the whole book only the farms that have data are offered. */}
                          {[...jdFarmsOnScreen,
                            ...(filteredFarms.length > FARM_CHOICE_CAP ? [] : filteredFarms.filter(f => !(jdDataSet && jdDataSet.has(f.id))))].map(f => (
                            <button key={f.id} onClick={() => setSelectedFarmerId(f.id)}
                              title={jdDataSet && jdDataSet.has(f.id) ? "Has machine data" : "No machine data on file yet"}
                              style={{ border:"1px solid #cdd9cd", borderRadius:999, padding:"6px 14px", fontSize:12, fontWeight:700,
                                       background: jdDataSet && jdDataSet.has(f.id) ? "#f6faf6" : "#fafbfa",
                                       color: jdDataSet && jdDataSet.has(f.id) ? "#2d4a2d" : "#8a978a",
                                       cursor:"pointer", fontFamily:"inherit" }}>
                              {/* Which of them actually HAS machine data is the thing that
                                  decides the pick, so it is on the button rather than one
                                  click further in. */}
                              {f.name}
                              {!(jdDataSet && jdDataSet.has(f.id)) ? <span style={{ fontWeight:500 }}> · no machine data</span> : null}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : <>Pick a <b>farm</b> above to see its planting, harvest, yield maps, and claim attribution — or open <b>Connected accounts</b> to connect more.</>}
              </div>
            );
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {agencyBar}
                {hasJd ? (
                  <JdFieldDataView key={jdFarm} farmer={{ id: jdFarm, name: jdName }}
                    farmerId={jdFarm} embedded portal={isPortal} initialYear={fieldYear || CURRENT_CROP_YEAR} />
                ) : empty}
                {/* UPLOAD A YIELD MAP FROM ANY MONITOR — Kevin, 2026-10-01: "I agree it should
                    be in field mapper and then machine data". Below the records as well as in
                    place of the empty state, because a farm can run John Deere on the combine
                    and a Trimble or an Ag Leader on the planter.
                    ⚠ authedFetch and the uploader are passed IN rather than reached for: this
                    tab renders in the PORTAL too, and a component that resolves its own
                    session there is the shape check-home-safe exists to forbid. */}
                {jdFarm ? (
                  <YieldImportPanel farmerId={jdFarm} farmName={jdName} isMobile={isMobile}
                    authedFetch={authedFetch}
                    uploadFile={(key, f, onProgress) => sbStorageUploadProgress("farmer-documents", key, f, onProgress)}
                    onDone={() => setGeomNonce((n) => n + 1)} />
                ) : null}
              </div>
            );
          })()}

          {/* MISSING-BOUNDARY WORKLIST — insured fields with no shapefile, grouped by farmer */}
          {view === "missing" && (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {!geomClus ? (
                <div style={{ ...card, color:"#60735f" }}>Checking boundaries…</div>
              ) : missing.length === 0 ? (
                <div style={{ ...card, color:"#2d6a2d", fontWeight:700 }}>
                  ✓ Every insured field in view has a boundary.
                  {/* ⚠ SCOPE, NOT A HEDGE. Without it this reads as "we have everything",
                      and the Machine Data tab's "not matched to an insured CLU" then reads
                      as a contradiction rather than as a different population. */}
                  <div style={{ fontWeight:500, fontSize:11.5, color:"#5c7a5c", marginTop:5, lineHeight:1.5 }}>
                    That covers the fields this farm <b>insures</b>. Machine acres can still fall
                    outside them — on rented or custom-harvested ground that carries no policy with
                    us — and those are counted separately under Machine&nbsp;Data.
                  </div>
                </div>
              ) : (() => {
                // group the missing fields by farmer so each is clearly attached to a grower
                const byFarmer = {};
                for (const r of missing) {
                  const fid = r.farmer_id || "_none";
                  (byFarmer[fid] = byFarmer[fid] || { id:fid, name: nameOf(r), fields:[], acres:0 });
                  byFarmer[fid].fields.push(r); byFarmer[fid].acres += acreOf(r);
                }
                const farms = Object.values(byFarmer).sort((a,b) => b.acres - a.acres || a.name.localeCompare(b.name));
                return (
                  <>
                    <div style={{ ...card, display:"flex", flexWrap:"wrap", gap:16, alignItems:"center" }}>
                      <div style={{ fontSize:13, color:"#5a3a10" }}>
                        <b style={{ fontSize:18, color:"#9a5a1a" }}>{missing.length}</b> insured field{missing.length===1?"":"s"} across <b>{missingFarms}</b> farm{missingFarms===1?"":"s"} have <b>no boundary</b>{missingAcres>0 ? <> · <b>{fmtAc(missingAcres)}</b> ac</> : null}
                        {noCluCount>0 && <div style={{ fontSize:11.5, color:"#9a8a6a", marginTop:3 }}>({noCluCount} grape/orchard field{noCluCount===1?"":"s"} excluded — the AIP feed gives them no CLU, so a boundary can’t attach)</div>}
                      </div>
                      <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
                        {!isPortal && <button onClick={()=>setImportOpen(true)} title="Import boundaries from a shapefile / GeoJSON — only the fields we're missing"
                          style={{ ...sel, cursor:"pointer", fontWeight:700, color:"#1a2e1a", background:"#eef7e8", borderColor:"#bcdcb4" }}>Import boundaries</button>}
                        <button onClick={exportMissingXlsx} title="Export this worklist to Excel"
                          style={{ ...sel, cursor:"pointer", fontWeight:700, color:"#2d4a2d" }}>Export to Excel</button>
                      </div>
                    </div>
                    {farms.map(g => {
                      const bid = resolveBookId(g);
                      const open = missingOpen.has(g.id);
                      const toggleMissing = () => setMissingOpen(prev => { const n = new Set(prev); n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n; });
                      return (
                        <div key={g.id} style={card}>
                          {/* farmer header — click anywhere to expand/collapse the field list */}
                          <div onClick={toggleMissing} style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", cursor:"pointer", marginBottom: open ? 8 : 0 }}>
                            <span style={{ color:"#60735f", fontSize:12, width:12, transform: open ? "rotate(90deg)" : "none", transition:"transform .12s" }}>▶</span>
                            {onSelect && bid && !isPortal
                              ? <button onClick={(e) => { e.stopPropagation(); onSelect(bid); }} title="Open farm card" style={{ background:"none", border:"none", padding:0, cursor:"pointer", textAlign:"left", color:"#1a2e1a", fontSize:15, fontWeight:800 }}>{g.name}</button>
                              : <span style={{ color:"#1a2e1a", fontSize:15, fontWeight:800 }}>{g.name}</span>}
                            <span style={{ fontSize:11, fontWeight:800, color:"#9a5a1a", background:"#fdf0e3", padding:"2px 9px", borderRadius:10 }}>{g.fields.length} field{g.fields.length===1?"":"s"} no boundary</span>
                            <span style={{ marginLeft:"auto", fontSize:12, color:"#60735f" }}>{g.acres>0 ? fmtAc(g.acres)+" ac" : "acres n/a"}</span>
                          </div>
                          {open && isMobile && (
                            // Phone: one line per field (farm · tract · field, crop, acres) in place
                            // of a seven-column table that ran off the screen.
                            <div>
                              {g.fields.slice().sort((a,b)=> (cty(a)||"").localeCompare(cty(b)||"") || (a.fsn||"").localeCompare(b.fsn||"",undefined,{numeric:true}) || (a.field_id||"").localeCompare(b.field_id||"",undefined,{numeric:true})).map((r,i) => (
                                <div key={i} style={{ display:"flex", alignItems:"baseline", gap:8, padding:"8px 0", borderTop:"1px solid #f0f4f0" }}>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a" }}>{[r.fsn, r.tract, r.field_id].map(x => x || "—").join("-")}</div>
                                    <div style={{ fontSize:12, color:"#4f6a4c" }}>{[fmCropName(r.crop) || null, cty(r) || null, aipLabel(r.aip)].filter(Boolean).join(" · ")}</div>
                                  </div>
                                  <div style={{ fontSize:13, fontWeight:700, fontVariantNumeric:"tabular-nums", flexShrink:0 }}>{acreOf(r) > 0 ? fmtAc(acreOf(r)) + " ac" : "—"}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {open && !isMobile && (
                          <div style={{ overflowX:"auto" }}>
                            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5, minWidth:520 }}>
                              <thead><tr style={{ color:"#60735f", fontSize:10.5, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                                <th style={{ textAlign:"left", padding:"5px 8px" }}>AIP</th>
                                <th style={{ textAlign:"left", padding:"5px 8px" }}>County</th>
                                <th style={{ textAlign:"left", padding:"5px 8px" }}>Farm</th>
                                <th style={{ textAlign:"left", padding:"5px 8px" }}>Tract</th>
                                <th style={{ textAlign:"left", padding:"5px 8px" }}>Field</th>
                                <th style={{ textAlign:"left", padding:"5px 8px" }}>Crop</th>
                                <th style={{ textAlign:"right", padding:"5px 8px" }}>Acres</th>
                              </tr></thead>
                              <tbody>
                                {g.fields.slice().sort((a,b)=> (cty(a)||"").localeCompare(cty(b)||"") || (a.fsn||"").localeCompare(b.fsn||"",undefined,{numeric:true}) || (a.field_id||"").localeCompare(b.field_id||"",undefined,{numeric:true})).map((r,i) => (
                                  <tr key={i} style={{ borderTop:"1px solid #f0f4f0" }}>
                                    <td style={{ padding:"5px 8px", color:"#60735f" }}>{aipLabel(r.aip)}</td>
                                    <td style={{ padding:"5px 8px", color:"#4a6e4a" }}>{cty(r) || "—"}</td>
                                    <td style={{ padding:"5px 8px" }}>{r.fsn || "—"}</td>
                                    <td style={{ padding:"5px 8px" }}>{r.tract || "—"}</td>
                                    <td style={{ padding:"5px 8px" }}>{r.field_id || "—"}</td>
                                    <td style={{ padding:"5px 8px", fontWeight:600 }}>{fmCropName(r.crop)}</td>
                                    <td style={{ padding:"5px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{acreOf(r) > 0 ? fmtAc(acreOf(r)) : <span style={{ color:"#bbb" }}>—</span>}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          )}

          {/* TREE VIEW */}
          {view === "tree" && (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {(selectionActive ? groups.filter(g => g.id === selectedFarmerId) : groups).map(g => {
                // when scoped to a farm (e.g. selected on the map), show it expanded
                const open = expanded.has(g.id) || (selectionActive && g.id === selectedFarmerId);
                // group fields by policy_number within the farmer
                const byPolicy = {};
                for (const f of g.fields) { (byPolicy[f.policy_number||"—"] = byPolicy[f.policy_number||"—"] || []).push(f); }
                return (
                  <div key={g.id} style={card}>
                    <div onClick={()=>{ toggle(g.id); setSelectedFarmerId(g.id); }} title="Select this farm for Weather / History" style={{ display:"flex", alignItems:"center", gap: isMobile ? 6 : 10, flexWrap: isMobile ? "wrap" : "nowrap", cursor:"pointer" }}>
                      <span style={{ fontSize:12, color:"#60735f", width:14 }}>{open?"▾":"▸"}</span>
                      <div style={{ fontWeight:800, color:"#1a2e1a", fontSize:15, flex: isMobile ? "1 1 calc(100% - 30px)" : 1, minWidth:0 }}>{g.name}</div>
                      {[...g.aips].map(a => (
                        <span key={a} style={{ fontSize:10, fontWeight:800, padding:"2px 7px", borderRadius:10,
                          background: a==="NAU"?"#e3eefc":"#fdf0e3", color: a==="NAU"?"#1f5fb0":"#b06a1f" }}>{aipLabel(a)}</span>
                      ))}
                      <span style={{ fontSize:12, color:"#60735f" }}>{g.fields.length} fields</span>
                      <span style={{ fontSize:12, color:"#60735f", minWidth:70, textAlign:"right" }}
                        title={g.noAcre ? `${g.noAcre} of ${g.fields.length} fields carry no reported acreage in the AIP file` : undefined}>
                        {g.noAcre === g.fields.length
                          ? <span style={{ color:"#a3b3a3" }}>no acreage reported</span>
                          : <>{fmtAc(g.acres)} ac{g.noAcre > 0 && <span style={{ color:"#b8860b" }}> +{g.noAcre} n/r</span>}</>}
                      </span>
                      <button onClick={(e)=>{ e.stopPropagation(); printFieldReport(g); }} title="Print / Save a PDF field report for this grower"
                        style={{ border:"1px solid #cdd9cd", background:"#f6faf6", borderRadius:6, padding:"3px 9px", fontSize:11, fontWeight:700, color:"#2d4a2d", cursor:"pointer" }}>
                        PDF report
                      </button>
                      {!isPortal && onSelect && resolveBookId(g) && (
                        <button onClick={(e)=>{ e.stopPropagation(); const bid = resolveBookId(g); if (bid) onSelect(bid); }}
                          style={{ border:"1px solid #cdd9cd", background:"#f6faf6", borderRadius:6, padding:"3px 9px", fontSize:11, fontWeight:700, color:"#2d4a2d", cursor:"pointer" }}>
                          Open card →
                        </button>
                      )}
                    </div>
                    {open && (
                      <div style={{ marginTop:10, marginLeft: isMobile?0:24 }}>
                        <div style={{ fontSize:11, color:"#60735f", marginBottom:6 }}>
                          {[...g.counties].join(", ")}{g.crops.size ? " · " + [...g.crops].join(", ") : ""}
                        </div>

                        {/* INSURANCE SUMMARY — schedule-of-insurance roll-up: acres + liability +
                            bushel guarantee, by crop × county (mirrors the R&H/NAU SOI) */}
                        {(() => {
                          const summ = buildCropSummary(g);
                          if (!summ.length) return null;
                          const totAc   = summ.reduce((s,r)=> s + r.acres, 0);
                          const totPP   = summ.reduce((s,r)=> s + (r.ppAcres||0), 0);
                          const totPPLiab = summ.reduce((s,r)=> s + (r.ppLiab||0), 0);
                          const totLiab = summ.reduce((s,r)=> s + (r.liab||0), 0);
                          const liabCell = (v) => v != null ? "$" + Math.round(v).toLocaleString() : "—";
                          const buCell   = (v) => v != null ? Math.round(v).toLocaleString() + " bu" : "—";
                          const anyMissing = guar && summ.some(r => r.liab == null);
                          return (
                            <div style={{ marginBottom:14, background:"#f6faf6", border:"1px solid #e3ebe3", borderRadius:8, padding:"8px 10px" }}>
                              <div style={{ fontSize:11, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase", color:"#4a6e4a", marginBottom:4 }}>
                                Insurance Summary {!guar && <span style={{ fontWeight:600, textTransform:"none", letterSpacing:0, color:"#677167" }}>· loading guarantees…</span>}
                              </div>
                              <div style={{ overflowX:"auto" }}>
                                <table style={{ width:"100%", borderCollapse:"collapse", minWidth: isMobile ? 0 : 520, fontSize: isMobile ? 12 : 12.5 }}>
                                  <thead><tr style={{ color:"#60735f", fontSize:10, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                                    <th style={{ textAlign:"left",  padding:"4px 8px" }}>Crop</th>
                                    <th style={{ textAlign:"left",  padding:"4px 8px" }}>County</th>
                                    <th style={{ textAlign:"right", padding:"4px 8px" }}>Acres</th>
                                    <th style={{ textAlign:"right", padding:"4px 8px" }}>Liability</th>
                                    <th style={{ textAlign:"right", padding:"4px 8px" }}>Bu Guarantee</th>
                                  </tr></thead>
                                  <tbody>
                                    {summ.map(r => {
                                      const single = r.rows.length <= 1;
                                      const only = r.rows[0];
                                      return (
                                        <React.Fragment key={r.key || r.crop}>
                                          {/* crop row — single county: carries that county's figures.
                                              multi-county: just a label; the counties (below) carry the
                                              figures and the grand Total footer sums them (no redundant subtotal). */}
                                          <tr style={{ borderTop:"1px solid #e3ebe3" }}>
                                            <td style={{ padding:"4px 8px", fontWeight:700, color:"#1f3320" }}>
                                              <span style={{ display:"inline-block", width:9, height:9, borderRadius:2, background: fieldCropColor(r.crop, r.type_code), marginRight:6, verticalAlign:"middle" }} />
                                              {fmCropName(r.crop)}
                                              {/* Corn only — grain and silage are separate products and separate
                                                  rows on the SOI, so they are separate rows here too. */}
                                              {r.typeLabel && <span style={{ fontWeight:600, color:"#60735f", fontSize:11.5 }}> · {r.typeLabel}</span>}
                                            </td>
                                            <td style={{ padding:"4px 8px", color:"#4a6e4a" }}>{single ? (only ? only.county : "—") : `${r.rows.length} counties`}</td>
                                            <td style={{ padding:"4px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums", fontWeight:700 }}>{single ? fmtAc(r.acres) : ""}</td>
                                            <td style={{ padding:"4px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{single ? liabCell(r.liab) : ""}</td>
                                            <td style={{ padding:"4px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{single ? buCell(r.bu) : ""}</td>
                                          </tr>
                                          {!single && r.rows.map(cr => (
                                            <React.Fragment key={r.crop+"|"+cr.county}>
                                              <tr>
                                                <td style={{ padding:"2px 8px" }} />
                                                <td style={{ padding:"2px 8px 2px 18px", color:"#60735f", fontSize:11.5 }}>{cr.county}</td>
                                                <td style={{ padding:"2px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums", color:"#4a6e4a" }}>{fmtAc(cr.acres)}</td>
                                                <td style={{ padding:"2px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums", color:"#4a6e4a" }}>{liabCell(cr.liab)}</td>
                                                <td style={{ padding:"2px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums", color:"#4a6e4a" }}>{buCell(cr.bu)}</td>
                                              </tr>
                                              {cr.ppAcres > 0 && (
                                                <tr>
                                                  <td style={{ padding:"2px 8px" }} />
                                                  <td style={{ padding:"2px 8px 2px 30px", color:"#9a5a1a", fontSize:11, fontWeight:600 }}>⬚ {cr.county} Prevent Plant</td>
                                                  <td style={{ padding:"2px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums", color:"#9a5a1a" }}>{fmtAc(cr.ppAcres)}</td>
                                                  <td style={{ padding:"2px 8px", textAlign:"right", color:"#c2b08a" }}>—</td>
                                                  <td style={{ padding:"2px 8px", textAlign:"right", color:"#c2b08a" }}>—</td>
                                                </tr>
                                              )}
                                            </React.Fragment>
                                          ))}
                                          {/* Single-county crop: PP belongs to that one county, shown beneath the crop row */}
                                          {single && r.ppAcres > 0 && (
                                            <tr key={r.crop+"|pp"}>
                                              <td style={{ padding:"2px 8px" }} />
                                              <td style={{ padding:"2px 8px 2px 18px", color:"#9a5a1a", fontSize:11.5, fontWeight:600 }}>⬚ Prevent Plant{only ? ` · ${only.county}` : ""}</td>
                                              <td style={{ padding:"2px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums", color:"#9a5a1a" }}>{fmtAc(r.ppAcres)}</td>
                                              <td style={{ padding:"2px 8px", textAlign:"right", color:"#c2b08a" }}>—</td>
                                              <td style={{ padding:"2px 8px", textAlign:"right", color:"#c2b08a" }}>—</td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot>
                                    {/* Grand total — only when there's more than one line to sum (multiple
                                        crops, or one crop across multiple counties). A single crop in a single
                                        county already IS the total, so skip the redundant footer. */}
                                    {(summ.length > 1 || summ.some(r => r.rows.length > 1)) && (
                                    <tr style={{ borderTop:"2px solid #d9e4d9", fontWeight:800, color:"#1a2e1a" }}>
                                      <td style={{ padding:"5px 8px" }}>Total{totPP > 0 ? " planted" : ""}</td>
                                      <td style={{ padding:"5px 8px", fontWeight:600, color:"#60735f" }}>{g.counties.size} {g.counties.size===1?"county":"counties"}</td>
                                      <td style={{ padding:"5px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{fmtAc(totAc)}</td>
                                      <td style={{ padding:"5px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{liabCell(totLiab > 0 ? totLiab : null)}</td>
                                      {/* No cross-crop bushel total — corn/soy/wheat bushels aren't additive; guar shown per crop only */}
                                      <td style={{ padding:"5px 8px", textAlign:"right", color:"#c2b08a" }}>—</td>
                                    </tr>
                                    )}
                                    {totPP > 0 && (summ.length > 1 || summ.some(r => r.rows.length > 1)) && (
                                      <tr style={{ fontWeight:700, color:"#9a5a1a" }}>
                                        <td style={{ padding:"3px 8px" }}>⬚ Prevent Plant</td>
                                        <td />
                                        <td style={{ padding:"3px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{fmtAc(totPP)}</td>
                                        <td style={{ padding:"3px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{liabCell(totPPLiab > 0 ? totPPLiab : null)}</td>
                                        <td style={{ padding:"3px 8px", textAlign:"right", color:"#c2b08a" }}>—</td>
                                      </tr>
                                    )}
                                  </tfoot>
                                </table>
                              </div>
                              <div style={{ fontSize:10.5, color:"#677167", marginTop:4 }}>
                                Liability &amp; bushel guarantee = base MPCI (RP/YP) per county, from the SOI (M13); SCO/ECO overlays excluded.
                                {totPP > 0 && " For counties with prevent plant, the liability shown is the blended SOI figure covering planted + PP acres."}
                                {anyMissing && " “—” = no base MPCI on that crop (e.g. PRF)."}
                              </div>
                            </div>
                          );
                        })()}

                        {Object.entries(byPolicy).map(([pn, fields]) => (
                          <div key={pn} style={{ marginBottom:10 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:"#2d4a2d", margin:"4px 0" }}>{pn} <span style={{ color:"#677167", fontWeight:500 }}>· {fields.length} fields</span>{(() => {
                              // CIMS as-of date across this policy's fields, shown as a RANGE
                              // because one policy can carry fields keyed off different CIMS
                              // snapshots — NAU's CY2026 book spans Aug 2025 to Jul 2026 while
                              // R&H's is almost entirely Apr 2026. The spread is the signal;
                              // no verdict attached.
                              const vs = fields.map(f => f.clu_release_date).filter(Boolean).sort();
                              if (!vs.length) return null;
                              const lo = vs[0], hi = vs[vs.length - 1];
                              const missing = fields.length - vs.length;
                              return (
                                <span style={{ color:"#677167", fontWeight:500 }} title="As-of date of the CIMS snapshot these fields were keyed against (P27 #20). NOT a CIMS run date — that is on RMA's ARDIFF report, which is not in the M13 file.">
                                  {" · CIMS as of "}{lo === hi ? fmtCluVintage(lo) : `${fmtCluVintage(lo)}–${fmtCluVintage(hi)}`}
                                  {missing > 0 ? ` · ${missing} not reported` : ""}
                                </span>
                              );
                            })()}</div>
                            {/* Phone: a line per field (farm-tract-field, crop, county, planted, acres)
                                instead of a ten-column table, with Weather on the row. */}
                            {isMobile && fields.slice().sort(cmpRows).map((f, fi) => {
                              const ac = f.acres != null ? Number(f.acres) : ((f.crop || "").toUpperCase() === "PRF" ? (cluAcres[(f.clu_id || "").toLowerCase()] ?? null) : null), pp = f.pp_acres != null ? Number(f.pp_acres) : 0;
                              return (
                                <div key={f.id} role="button" tabIndex={0} onClick={()=>openAcreageField(f)} onKeyDown={(e)=>{ if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAcreageField(f); } }}
                                  aria-label={`Open field ${[f.fsn, f.tract, f.field_id].map(x => x || "—").join("-")} on the map`}
                                  style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 0", minHeight:48, cursor:"pointer", borderTop: fi ? "1px solid #f0f4f0" : "none" }}>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a" }}>{[f.fsn, f.tract, f.field_id].map(x => x || "—").join("-")}</div>
                                    <div style={{ fontSize:12, color:"#4f6a4c" }}>
                                      {[fmCropName(f.crop), cty(f) || null, fmMd(f.report_date) ? `planted ${fmMd(f.report_date)}` : null].filter(Boolean).join(" · ")}
                                    </div>
                                  </div>
                                  <div style={{ textAlign:"right", flexShrink:0 }}>
                                    <div style={{ fontSize:13, fontWeight:700, fontVariantNumeric:"tabular-nums" }}>{Number.isFinite(ac) && ac > 0 ? `${fmtAc(ac)} ac` : "—"}</div>
                                    {Number.isFinite(pp) && pp > 0 && <div style={{ fontSize:11, fontWeight:700, color:"#9a5a1a" }}>{fmtAc(pp)} PP</div>}
                                  </div>
                                  <span aria-hidden="true" style={{ color:"#9aa89a", fontSize:20, lineHeight:1, flexShrink:0 }}>›</span>
                                </div>
                              );
                            })}
                            {!isMobile && <div style={{ overflowX:"auto" }}>
                              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:560 }}>
                                <thead><tr>
                                  <Th k="county">County</Th><Th k="fsn">FSN</Th><Th k="tract">Tract</Th><Th k="field_id">Field</Th><Th k="crop">Crop</Th><Th k="report_date">Planted</Th><Th k="clu_release_date">CIMS as of</Th><Th k="acres" right>Acres</Th><Th k="pp_acres" right>PP ac</Th>
                                </tr></thead>
                                <tbody>
                                  {fields.slice().sort(cmpRows).map(f => (
                                    <tr key={f.id} onClick={()=>openAcreageField(f)} title="Open this field on the map" style={{ cursor:"pointer" }}>{fieldCells(f)}</tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* TABLE VIEW */}
        </>
      )}
    </div>
  );
}

// ── MODEL SCORECARD (admin-only "secret page") ───────────────────────────
// Predicts an actual yield for every grower × crop (corn, soybeans) = the grower's
// recent APH baseline × the damped mechanistic weather index for their county, then
// FREEZES it to yield_predictions (snapshot_date). When M13 later pulls the realized
// yield into production_history, the same page scores every frozen snapshot against
// it — the only honest way to grade a forecast. Wheat is excluded (no validated
// in-season weather skill — see the Field Pulse backtest).
function YieldScorecardView({ farmers, isMobile }) {
  const yr = useMemo(() => new Date().getUTCFullYear(), []);
  const [fields, setFields] = useState(null);     // keyed planted corn/soy fields, this year
  const [cells, setCells] = useState(null);       // wny_grid_cells (high-def ~4 km)
  const [centroids, setCentroids] = useState(null); // clu_id(lower) → [lat,lon] (NAU fields)
  const [wx, setWx] = useState(null);             // cell_id → { daily, climo }
  const [fieldPr, setFieldPr] = useState(null);   // clu(lower) → { obs_date: precip_mm } — per-field 1 km rain
  const [cc, setCc] = useState(null);             // crop_coverage — OFFICIAL approved APH
  const [ph, setPh] = useState(null);             // production_history — actuals + fallback baseline
  const [snaps, setSnaps] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [cropF, setCropF] = useState("ALL");
  const [stateF, setStateF] = useState("ALL");
  const [countyF, setCountyF] = useState("ALL");
  const [sortKey, setSortKey] = useState("grower");
  const [sortDir, setSortDir] = useState("asc");
  const [snapDate, setSnapDate] = useState("");
  // ── WHOSE SCORECARD. Kevin, 2026-10-09: "Model scorecard also takes forever to load,
  // is there a way we could select 1 farm to check the model scorecard on rather than
  // run the whole book?"
  //
  // Measured 2026-10-09 — the four reads this narrows, paged exactly as the page pages
  // them, whole book against HOWLETT FARMS INC:
  //
  //     whole book    44 requests   41,446 rows   6.13 MB   5,186ms
  //     one farm       4 requests      110 rows   0.02 MB     240ms
  //
  // and the weather reads shrink with it, because they are keyed on the CELLS these
  // fields touch — 347 book-wide against a handful for one farm. End to end the book
  // run needs a 280-SECOND wait to finish rendering; a farm run is done in moments.
  //
  // "" means NOTHING IS LOADED YET. The page used to start the whole-book run on mount,
  // which is the thing being complained about; it now asks first and says what each
  // choice costs. The book is still one click away and reads EXACTLY as it did before —
  // every query below is unchanged when `farmSel === "ALL"`, so the whole-book answer
  // cannot have moved.
  const [farmSel, setFarmSel] = useState("");
  const scoped = farmSel && farmSel !== "ALL" ? farmSel : null;
  const loadBook = farmSel === "ALL";
  const armed = farmSel !== "";
  const toggleSort = (k) => { if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir(k === "grower" || k === "crop" || k === "county" || k === "planted" ? "asc" : "desc"); } };
  const nameById = useMemo(() => { const m = {}; for (const f of (farmers || [])) m[f.id] = f.name; return m; }, [farmers]);

  // ── A SEARCH, NOT A DROPDOWN ─────────────────────────────────────
  // A <select> of every grower in the book is a list you scroll past, not a name you
  // type: 281 options, alphabetical, and the native control truncates the long ones.
  //
  // `farmQ` is the TYPED TEXT and `farmSel` is the COMMITTED choice, and keeping them
  // apart is the whole point — typing must not start a run. Every read below is gated
  // on `armed`, which only `farmSel` moves, so a half-typed name costs nothing.
  const [farmQ, setFarmQ] = useState("");
  const farmMatches = useMemo(() => {
    const all = (farmers || []).slice()
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    const s = farmQ.trim().toLowerCase();
    if (!s) return all;
    // EVERY typed word must appear somewhere in the name, in any order, so "howlett
    // farms" and "farms howlett" find the same grower and a doubled space costs
    // nothing. A plain substring test would miss the second of those.
    const words = s.split(/\s+/);
    return all.filter(f => {
      const n = String(f.name || "").toLowerCase();
      return words.every(w => n.includes(w));
    });
  }, [farmers, farmQ]);

  // 1) Keyed planted fields this year (acres + a real planting date). Drives the whole
  //    page off what's been keyed — so it auto-fills as M13 pulls acreage in.
  // ⚠ NOTHING LOADS UNTIL A CHOICE IS MADE, and `scoped` only ADDS a filter — the
  // whole-book branch issues the same query it always did, so "all farms" is provably
  // the same run as before this picker existed.
  useEffect(() => { let alive = true;
    if (!armed) { setFields(null); return; }
    sbFetch("policy_fields", { select: "crop,county,state_fips,report_date,acres,clu_id,latitude,longitude,policies(farmer_id)", filters: ["crop=in.(CORN,SOYBEANS)", `report_date=gte.${yr}-01-01`, "acres=gt.0", ...(scoped ? [`farmer_id=eq.${scoped}`] : [])], order: "id.asc" })
      .then(r => alive && setFields((r || []).filter(x => x.report_date))).catch(() => alive && setFields([]));
    return () => { alive = false; }; }, [yr, armed, scoped]);
  useEffect(() => { let alive = true;
    // state_fips rides along because the county-name fallback below needs it: the
    // grid left New York on 2026-09-02 and SIXTEEN county names now exist in two
    // states at once (ERIE, MONROE, WARREN, CLINTON, FRANKLIN, WYOMING, WASHINGTON,
    // GREENE, SULLIVAN, TIOGA, JEFFERSON, WAYNE, COLUMBIA, DELAWARE, FULTON,
    // MONTGOMERY). A bare county name is no longer a county.
    if (!armed) { setCells(null); return; }
    sbFetch("wny_grid_cells", { select: "id,lat,lon,county_name,state_fips,corn_acres,awc", order: "id.asc" })
      .then(c => alive && setCells((c || []).map(x => ({ id: x.id, lat: Number(x.lat), lon: Number(x.lon), county: String(x.county_name || "").toUpperCase(), st: String(x.state_fips || ""), corn_acres: Number(x.corn_acres) || 0, awc: x.awc != null ? Number(x.awc) : null })))).catch(() => alive && setCells([]));
    return () => { alive = false; }; }, [armed]);
  // ⚠ 24,715 ROWS / 2.5 MB, AND IT HAS NO FARMER COLUMN — only `clu_id`. So a scoped
  // run has to WAIT for `fields` and ask for that farm's CLUs, which is a sequencing
  // change the whole-book branch deliberately does not take: with no farm selected it
  // fires immediately and unfiltered, exactly as before.
  //
  // These centroids are only ever a FALLBACK for a field with no lat/lon of its own, so
  // narrowing them to the farm's own CLUs cannot change any other farm's answer — and
  // when the book is loaded, every CLU is still fetched.
  useEffect(() => { let alive = true;
    if (!armed) { setCentroids(null); return; }
    if (scoped && !fields) return;                 // wait for the CLU list, then scope
    // ⚠ FOLD THE CLU. These ids come off `policy_fields`, which is 59% UPPER-case,
    // and `field_geometries.clu_id` is lower on every row — so an exact `in.()`
    // silently returns nothing for most of them and the centroids come back empty,
    // which reads as "this farm has no boundaries" rather than as a bad filter.
    // `cluLower` is the one folding expression; the lookup below already folds.
    const clus = scoped ? [...new Set((fields || []).map(f => cluLower(f.clu_id)).filter(Boolean))] : null;
    if (clus && !clus.length) { setCentroids({}); return; }
    sbFetch("field_geometries", { select: "clu_id,centroid_lat,centroid_lon", order: "clu_id.asc", ...(clus ? { filters: [`clu_id=in.(${clus.join(",")})`] } : {}) })
      .then(g => { if (!alive) return; const m = {}; for (const r of (g || [])) if (r.centroid_lat != null) m[String(r.clu_id || "").toLowerCase()] = [Number(r.centroid_lat), Number(r.centroid_lon)]; setCentroids(m); }).catch(() => alive && setCentroids({}));
    return () => { alive = false; }; }, [armed, scoped, fields]);
  // `crop_coverage` carries no farmer column — only `policy_id` — so a scoped run
  // filters through the embed it already selects. `!inner` is what makes an embedded
  // filter actually narrow the parent rather than just null the embed.
  useEffect(() => { let alive = true;
    if (!armed) { setCc(null); return; }
    sbFetch("crop_coverage", scoped
      ? { select: "crop,county,aph_yield,plan,type,acres,pp_acres,policies!inner(farmer_id)", filters: ["crop=in.(CORN,SOYBEANS)", `policies.farmer_id=eq.${scoped}`], order: "id.asc" }
      : { select: "crop,county,aph_yield,plan,type,acres,pp_acres,policies(farmer_id)", filters: ["crop=in.(CORN,SOYBEANS)"], order: "id.asc" })
      .then(r => alive && setCc(r || [])).catch(() => alive && setCc([])); return () => { alive = false; }; }, [armed, scoped]);
  useEffect(() => { let alive = true;
    if (!armed) { setPh(null); return; }
    sbFetch("production_history", { select: "farmer_id,crop,county,state,crop_year,yield_per_acre,record_type,unit_of_measure,type", filters: ["crop=in.(CORN,SOYBEANS)", "record_type=in.(A,PA)", "yield_per_acre=gt.0", `crop_year=gte.${yr - 6}`, ...(scoped ? [`farmer_id=eq.${scoped}`] : [])], order: "id.asc" })
      .then(r => alive && setPh(r || [])).catch(() => alive && setPh([])); return () => { alive = false; }; }, [yr, armed, scoped]);
  // GRAIN only — corn silage is stored as crop='CORN' but in TONS (type GSS), so it
  // must never be averaged into a bushel APH (it craters dairies). See "anchor" fix.
  const isGrain = (r) => String(r.unit_of_measure || "").toUpperCase() !== "TON" && String(r.type || "").toUpperCase() !== "GSS";
  const loadSnaps = () => sbFetch("yield_predictions", { select: "*", filters: [`crop_year=eq.${yr}`, ...(scoped ? [`farmer_id=eq.${scoped}`] : [])], order: "snapshot_date.desc" }).then(r => setSnaps(r || [])).catch(() => setSnaps([]));
  useEffect(() => { if (armed) loadSnaps(); else setSnaps(null); }, [yr, armed, scoped]); // eslint-disable-line
  const snapDates = useMemo(() => snaps ? [...new Set(snaps.map(s => s.snapshot_date))] : [], [snaps]);

  // 2) Resolve every keyed field to its nearest high-def cell (field lat/lon → CLU
  //    centroid → county representative cell as last resort).
  const fieldCells = useMemo(() => {
    if (!fields || !cells || centroids == null) return null;
    // KEYED state|county, NOT county. This map answers "which cell speaks for this
    // county" for a field with no centroid, and it picks the cell carrying the most
    // corn. Under a bare-name key a Pennsylvania Erie cell and a New York Erie cell
    // are ONE county and the winner is whichever has more CDL corn — so a NY field
    // could be handed PA weather, and the swap would be invisible on every screen.
    // Same class as the 2026-09-01 `PA|HARFORD` lookup that printed "—" for a
    // Maryland county for want of the row's own state.
    const repByCty = {}; for (const c of cells) { const k = `${c.st}|${c.county}`; if (!repByCty[k] || c.corn_acres > repByCty[k].corn_acres) repByCty[k] = c; }
    const out = [];
    for (const f of fields) {
      const fid = f.policies && f.policies.farmer_id; if (!fid) continue;
      const cty = String(f.county || "").toUpperCase();
      const fSt = String(f.state_fips || "");
      const loc = (f.latitude != null && f.longitude != null) ? [Number(f.latitude), Number(f.longitude)] : (centroids[String(f.clu_id || "").toLowerCase()] || null);
      let cell = null;
      if (loc) { let bd = Infinity; for (const c of cells) { const d = haversineMi(loc, [c.lat, c.lon]); if (d < bd) { bd = d; cell = c; } } if (bd > 30) cell = null; }
      // A row carrying no state_fips cannot be placed by name at all. Refusing costs
      // that field its weather; guessing costs it another state's.
      if (!cell) cell = (fSt ? repByCty[`${fSt}|${cty}`] : null) || null;
      if (!cell) continue;
      out.push({ fid, crop: f.crop, county: cty, acres: Number(f.acres) || 0, plant: f.report_date, cell, clu: String(f.clu_id || "").toLowerCase() });
    }
    return out;
  }, [fields, cells, centroids]);
  const cellIds = useMemo(() => fieldCells ? [...new Set(fieldCells.map(f => f.cell.id))] : [], [fieldCells]);

  // 3) Weather for just the cells the book actually touches (Apr 1 → now + forecast).
  useEffect(() => { let alive = true;
    if (!cellIds.length) { setWx({}); return; }
    setWx(null);
    const inList = `in.(${cellIds.join(",")})`;
    Promise.all([
      // maxPages raised off the 50-page (50k-row) default on BOTH. At the default,
      // climo truncated at 50k and the cells past the cut came back with an empty
      // climo[] — buildFieldPulse skips those, so their growers vanished from the
      // index with no error. Daily is ordered ASC, so ITS cap drops the NEWEST days
      // and freezes the freshness stamp. Two different silent failures from one
      // default.
      //
      // THE CEILING IS A FUNCTION OF cellIds, AND THE CELL TABLE TRIPLED
      // (338 → 1,271 on 2026-08-13). This fetch is filtered to the cells the BOOK
      // touches, not the whole table, so it did not move much — measured 59 cells
      // from field coordinates, plus at most one representative cell per county
      // through the repByCty fallback. But at maxPages 150 the binding constraint
      // was climatology at 150k ÷ 365 = 411 cells, which is no longer comfortably
      // far away now that a WNY-adjacent field can snap to a real eastern cell
      // instead of a distant western one. Sized to the whole table (1,271 × 365 =
      // 464k) so the book can grow east without re-deriving this. maxPages is a
      // ceiling, not a target — the pager stops at the first short page — so the
      // headroom costs nothing.
      sbFetch("wny_grid_weather_daily", { select: "cell_id,obs_date,tmax_c,tmin_c,precip_mm,srad_wm2,vpd_kpa,is_forecast", filters: [`cell_id=${inList}`, `obs_date=gte.${yr}-04-01`], order: "obs_date.asc,cell_id.asc", maxPages: 500 }),
      sbFetch("wny_grid_climatology", { select: "cell_id,day_of_year,tmax_c,tmin_c,precip_mm,srad_wm2,vpd_kpa", filters: [`cell_id=${inList}`], order: "cell_id.asc,day_of_year.asc", maxPages: 500 }),
    ]).then(([d, c]) => { if (!alive) return; const dm = {}, cm = {}; for (const r of (d || [])) (dm[r.cell_id] = dm[r.cell_id] || []).push(r); for (const r of (c || [])) (cm[r.cell_id] = cm[r.cell_id] || []).push(r); const w = {}; for (const id of cellIds) w[id] = { daily: dm[id] || [], climo: cm[id] || [] }; setWx(w); }).catch(() => alive && setWx({}));
    return () => { alive = false; }; }, [cellIds, yr]);

  // 3b) Each keyed field's OWN native ~1 km rainfall (field_precip_daily) so the weather
  //     index is field-specific — two fields in one ~4 km cell diverge by their actual rain.
  useEffect(() => { let alive = true;
    const clus = fieldCells ? [...new Set(fieldCells.map(f => f.clu).filter(Boolean))] : [];
    if (!clus.length) { setFieldPr({}); return; }
    setFieldPr(null);
    (async () => { const acc = {};
      for (let i = 0; i < clus.length; i += 120) {
        const batch = clus.slice(i, i + 120);
        const rP = await sbFetch("field_precip_daily", { select: "clu_id,obs_date,precip_mm", filters: [`clu_id=in.(${batch.join(",")})`, `obs_date=gte.${yr}-04-01`], order: "clu_id.asc,obs_date.asc" });
        for (const p of (rP || [])) { if (p.precip_mm == null) continue; const k = (p.clu_id || "").toLowerCase(); (acc[k] = acc[k] || {})[p.obs_date] = Number(p.precip_mm); }
      }
      if (alive) setFieldPr(acc);
    })().catch(() => alive && setFieldPr({}));
    return () => { alive = false; }; }, [fieldCells, yr]);

  // Official coverage per (grower, crop, COUNTY) — the row spine of the scorecard.
  // County + planted acres come from crop_coverage (the insurance unit / SOI), NOT from
  // the field records: a field's P27 county FIPS is the FSA admin county and can mis-tag
  // a field to the wrong county (e.g. Acomb's Steuben corn showing as Livingston). The
  // coverage county matches the field geometry on the map. APH = grain max over base
  // RP/YP rows; planted = base acres − prevent-plant acres.
  const covByKey = useMemo(() => {
    const m = {};
    for (const r of (cc || [])) {
      const fid = r.policies && r.policies.farmer_id; if (!fid) continue;
      const plan = String(r.plan || "").toUpperCase(); if (plan.includes("ECO") || plan.includes("SCO") || plan === "ARPI") continue;
      if (String(r.type || "").toUpperCase() === "GSS") continue;   // skip corn silage (tons), grain APH only
      const k = `${fid}|${r.crop}|${String(r.county || "").toUpperCase()}`;
      const e = m[k] || (m[k] = { aph: null, planted: 0 });
      const aph = parseFloat(r.aph_yield); if (Number.isFinite(aph) && aph > 0 && (e.aph == null || aph > e.aph)) e.aph = aph;
      const ac = parseFloat(r.acres), pp = parseFloat(r.pp_acres);
      if (Number.isFinite(ac)) e.planted += Math.max(0, ac - (Number.isFinite(pp) ? pp : 0));
    }
    return m;
  }, [cc]);
  // production_history: actuals (current yr) + fallback baseline (recent prior mean)
  // Actual current-year GRAIN yield once M13 pulls it in — for scoring snapshots
  const actualByKey = useMemo(() => { const m = {}, n = {}; for (const r of (ph || [])) { if (!isGrain(r) || Number(r.crop_year) !== yr) continue; const k = `${r.farmer_id}|${r.crop}|${String(r.county || "").toUpperCase()}`; m[k] = (m[k] || 0) + Number(r.yield_per_acre); n[k] = (n[k] || 0) + 1; } const out = {}; for (const k in m) out[k] = m[k] / n[k]; return out; }, [ph, yr]);
  const ctyState = useMemo(() => { const m = {}; for (const r of (ph || [])) { const c = String(r.county || "").toUpperCase(); if (c && r.state) m[c] = String(r.state).toUpperCase(); } return m; }, [ph]);
  // Freshness of the observed weather driving every prediction — latest non-forecast
  // obs_date across all touched cells. Stalls if the gridMET/MRMS pipeline stops.
  const wxThrough = useMemo(() => {
    if (!wx) return null;
    let mx = null;
    for (const id in wx) for (const r of (wx[id].daily || [])) if (!r.is_forecast && r.obs_date && (!mx || r.obs_date > mx)) mx = r.obs_date;
    return mx;
  }, [wx]);

  // 4) Per-field pulse (sim-cached by cell+plant+crop) → acre-weighted to a grower×crop
  //    WEATHER INDEX, then applied to each coverage county's official APH. County + acres
  //    are the coverage figures (covByKey), so they match the map / SOI; the index rides
  //    along from the keyed fields. (Fields feed the index only — their P27 county is NOT
  //    used to bucket, which is what mis-tagged Acomb's Steuben corn as Livingston.)
  const live = useMemo(() => {
    if (!fieldCells || !wx || fieldPr === null) return null;
    const cache = {};
    // cache key includes CLU: same cell+plant can still differ by the field's own rain.
    const pulseOf = (f) => { const k = `${f.cell.id}|${f.clu}|${f.plant}|${f.crop}`; if (k in cache) return cache[k]; const w = wx[f.cell.id]; const p = (w && w.daily.length && w.climo.length) ? buildFieldPulse(w.daily, w.climo, f.cell.awc, f.plant, f.crop, fieldPr[f.clu]) : null; cache[k] = (p && !p.noPlantDate && p.mechFactor != null) ? p : null; return cache[k]; };
    // (a) weather index per grower×crop, acre-weighted across ALL that grower's keyed fields
    const gc = {};
    for (const f of fieldCells) {
      const p = pulseOf(f); if (!p) continue;
      const key = `${f.fid}|${f.crop}`;
      const a = gc[key] = gc[key] || { acW: 0, mfNum: 0, idxNum: 0, idxW: 0, plantSum: 0, plantW: 0, pMin: null, pMax: null, nFields: 0 };
      const ac = f.acres > 0 ? f.acres : 1;
      a.acW += ac; a.mfNum += ac * p.mechFactor; a.nFields += 1;
      if (p.indexRatio != null) { a.idxNum += ac * p.indexRatio; a.idxW += ac; }
      const pd = f.plant ? miPulseDoy(f.plant.slice(0, 10)) : null;
      if (pd) { a.plantSum += ac * pd; a.plantW += ac; a.pMin = a.pMin == null ? pd : Math.min(a.pMin, pd); a.pMax = a.pMax == null ? pd : Math.max(a.pMax, pd); }
    }
    // (b) one row per coverage county — official APH × the grower×crop index
    const rows = [];
    for (const [key, cov] of Object.entries(covByKey)) {
      const [fid, crop, cty] = key.split("|");
      if (cov.aph == null || cov.planted <= 0) continue;
      const g = gc[`${fid}|${crop}`]; if (!g || g.acW <= 0) continue;   // no keyed fields → can't predict
      const mf = g.mfNum / g.acW;
      const idx = g.idxW > 0 ? g.idxNum / g.idxW : null;
      const plantDoy = g.plantW > 0 ? Math.round(g.plantSum / g.plantW) : null;
      rows.push({ fid, crop, county: cty, state: ctyState[cty] || "", acres: Math.round(cov.planted), nFields: g.nFields, plantDoy, plantMin: g.pMin, plantMax: g.pMax, baseline: cov.aph, mf, idx, predicted: cov.aph * mf, actual: actualByKey[key] ?? null });
    }
    return rows;
  }, [fieldCells, wx, fieldPr, covByKey, actualByKey, ctyState]);

  const view = useMemo(() => {
    let rows;
    if (snapDate && snaps) {
      rows = snaps.filter(s => s.snapshot_date === snapDate).map(s => { const cty = String(s.county || "").toUpperCase(); return {
        fid: s.farmer_id, crop: s.crop, county: cty, state: ctyState[cty] || "", acres: s.acres, nFields: s.n_fields, plantDoy: s.planted_doy,
        baseline: Number(s.baseline_aph), mf: Number(s.mech_factor), idx: s.index_ratio != null ? Number(s.index_ratio) : null,
        predicted: Number(s.predicted_yield), actual: actualByKey[`${s.farmer_id}|${s.crop}|${cty}`] ?? null }; });
    } else rows = live || [];
    if (cropF !== "ALL") rows = rows.filter(r => r.crop === cropF);
    if (stateF !== "ALL") rows = rows.filter(r => (r.state || "") === stateF);
    if (countyF !== "ALL") rows = rows.filter(r => r.county === countyF);
    const dir = sortDir === "asc" ? 1 : -1;
    const sv = (r) => { switch (sortKey) {
      case "crop": return r.crop; case "county": return r.county || ""; case "planted": return r.plantDoy ?? 9999;
      case "acres": return r.acres ?? -1; case "baseline": return r.baseline ?? -1; case "index": return r.mf ?? -1;
      case "predicted": return r.predicted ?? -1; case "actual": return r.actual ?? -1;
      case "error": return (r.actual != null && r.predicted != null) ? Math.abs(r.predicted - r.actual) : -1;
      default: return nameById[r.fid] || "~";
    } };
    const isStr = sortKey === "grower" || sortKey === "crop" || sortKey === "county";
    return rows.slice().sort((a, b) => { const va = sv(a), vb = sv(b); return (isStr ? String(va).localeCompare(String(vb)) : (va - vb)) * dir; });
  }, [snapDate, snaps, live, cropF, stateF, countyF, sortKey, sortDir, actualByKey, nameById, ctyState]);

  const stateOpts = useMemo(() => [...new Set((live || []).map(r => r.state).filter(Boolean))].sort(), [live]);
  const countyOpts = useMemo(() => [...new Set((live || []).filter(r => stateF === "ALL" || r.state === stateF).map(r => r.county).filter(Boolean))].sort(), [live, stateF]);

  const scored = useMemo(() => view.filter(r => r.actual != null && r.predicted != null), [view]);
  const mae = scored.length ? scored.reduce((s, r) => s + Math.abs(r.predicted - r.actual), 0) / scored.length : null;
  const baseMae = scored.length ? scored.reduce((s, r) => s + Math.abs(r.baseline - r.actual), 0) / scored.length : null;

  const snapshot = async () => {
    if (!live) return;
    setBusy(true); setStatus("");
    try {
      const today = new Date().toISOString().slice(0, 10);
      const rows = live.filter(p => p.predicted != null).map(p => ({
        farmer_id: p.fid, crop: p.crop, county: p.county, crop_year: yr,
        baseline_aph: +p.baseline.toFixed(1), baseline_source: "APH", mech_factor: +p.mf.toFixed(4),
        index_ratio: p.idx != null ? +p.idx.toFixed(4) : null, predicted_yield: +p.predicted.toFixed(1),
        acres: p.acres != null ? Math.round(p.acres) : null, n_fields: p.nFields, planted_doy: p.plantDoy,
        model_version: p.crop === "CORN" ? "corn-mi-v2-field" : "soy-mi-v2-field", snapshot_date: today,
      }));
      if (!rows.length) { setStatus("Nothing to snapshot yet (no predictions computed)."); setBusy(false); return; }
      await sbUpsert("yield_predictions", rows, "farmer_id,crop,county,crop_year,snapshot_date");
      setStatus(`📸 Froze ${rows.length} predictions for ${today}. They’ll score against actuals as M13 pulls ${yr} production.`);
      loadSnaps();
    } catch (e) { setStatus("Snapshot failed: " + e.message); }
    setBusy(false);
  };

  const card = { background: "#fff", border: "1px solid #d9e4d9", borderRadius: 10, padding: "12px 14px" };
  const sel = { padding: "7px 10px", borderRadius: 8, border: "1px solid #cdd9cd", background: "#fff", fontSize: 13, color: "#1a2e1a" };
  const loading = !live;
  const cropTag = (c) => c === "CORN" ? "🌽" : "🫘";
  const doyMD = (d) => { if (d == null) return "—"; const dt = new Date(Date.UTC(yr, 0, 1) + (d - 1) * 86400000); return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`; };
  const COLS = [["grower", "Grower", 0], ["crop", "Crop", 0], ["county", "County", 0], ["planted", "Planted", 0], ["acres", "Acres", 1], ["baseline", "APH", 1], ["index", "× Index", 1], ["predicted", "Predicted", 1], ["actual", `Actual ${yr}`, 1], ["error", "Error", 1]];

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <h2 style={{ ...uiPageTitle(isMobile) }}>Yield Model Scorecard</h2>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#9a5a1a", background: "#fdf5e6", border: "1px solid #f0e0c0", padding: "2px 8px", borderRadius: 10 }}>admin only</span>
      </div>
      <div style={{ fontSize: 12.5, color: "#54724f", marginBottom: 14, lineHeight: 1.5 }}>
        Predicted {yr} yield per grower × crop × county = <b>official APH × a per-field weather index</b> — each keyed field run on its own ~4 km high-def cell with its real planting date, acre-weighted up to the grower (corn &amp; soybeans). Only growers with <b>{yr} acres keyed</b> appear; the page fills in as M13 pulls acreage. <b>Snapshot</b> freezes today’s predictions to score against M13 actuals later.
      </div>

      {/* ── WHOSE SCORECARD, asked BEFORE anything is read ────────────────────
          The page used to start the whole-book run on mount: 6.13 MB over 44
          requests before a single day of weather, and ~280s to finish rendering. It
          asks first now, and says what each choice costs so the wait is a decision
          rather than a surprise. */}
      <div style={{ ...card, marginBottom: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: "#1a2e1a" }}>Run for</span>
        {scoped ? (
          <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <b style={{ fontSize: 13.5, color: "#1a2e1a" }}>{nameById[scoped] || "this farm"}</b>
            <button onClick={() => { setFarmSel(""); setFarmQ(""); }}
              style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid #d5e2d5", background: "#fff", cursor: "pointer", color: "#54724f" }}>change</button>
          </span>
        ) : (
          <span style={{ position: "relative", display: "inline-block" }}>
            <input value={farmQ} onChange={e => setFarmQ(e.target.value)}
              placeholder="Search a farm…" autoComplete="off" autoCorrect="off" spellCheck={false}
              name="scorecard-farm-search-x"
              style={{ width: 260, maxWidth: "100%", fontSize: 13.5, padding: "8px 11px", borderRadius: 8, border: "1px solid #d5e2d5", boxSizing: "border-box" }} />
            {/* The list is only drawn once something is typed — 281 names hanging open
                under the box on arrival is the dropdown again, in a different costume. */}
            {farmQ.trim() && (
              <div style={{ position: "absolute", zIndex: 30, top: "100%", left: 0, marginTop: 4, width: 300, maxWidth: "90vw", background: "#fff", border: "1px solid #e4ede4", borderRadius: 8, maxHeight: 260, overflowY: "auto", boxShadow: "0 6px 18px rgba(26,46,26,.12)" }}>
                {farmMatches.length === 0 && (
                  <div style={{ padding: "8px 12px", fontSize: 12.5, color: "#a5461b" }}>
                    No grower matches “{farmQ.trim()}”.
                  </div>
                )}
                {farmMatches.slice(0, 40).map(f => (
                  <div key={f.id} onClick={() => { setFarmSel(f.id); setFarmQ(""); }}
                    style={{ padding: "7px 12px", fontSize: 13.5, cursor: "pointer", borderBottom: "1px solid #f2f7f2", color: "#1a2e1a" }}>{f.name}</div>
                ))}
                {/* A truncated list that does not say so reads as “that grower is not in
                    the book”, which is a claim about the data rather than about the list. */}
                {farmMatches.length > 40 && (
                  <div style={{ padding: "6px 12px", fontSize: 11, color: "#60735f" }}>
                    {farmMatches.length - 40} more — keep typing to narrow it.
                  </div>
                )}
              </div>
            )}
          </span>
        )}
        {!loadBook && (
          <button onClick={() => { setFarmSel("ALL"); setFarmQ(""); }}
            style={{ fontSize: 11.5, padding: "5px 11px", borderRadius: 7, border: "1px solid #d5e2d5", background: "#f7faf7", cursor: "pointer", fontWeight: 700, color: "#54724f" }}>
            All farms (the whole book — slow)
          </button>
        )}
        {loadBook && (
          <button onClick={() => { setFarmSel(""); setFarmQ(""); }}
            style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid #d5e2d5", background: "#fff", cursor: "pointer", color: "#54724f" }}>change</button>
        )}
        {!armed && (
          <span style={{ fontSize: 12, color: "#7a6a2f" }}>
            One farm reads about 110 rows and returns in a moment; the whole book reads
            41,446 rows over 44 requests before any weather, and takes a few minutes.
            Nothing is loaded until you pick.
          </span>
        )}
        {loadBook && (
          <span style={{ fontSize: 12, color: "#7a6a2f" }}>
            Whole book — every grower, as before.
          </span>
        )}
      </div>

      {armed && (<>
      <div style={{ ...card, marginBottom: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={snapshot} disabled={busy || loading} style={{ ...sel, cursor: busy || loading ? "default" : "pointer", fontWeight: 800, background: "#1a2e1a", color: "#a8d878", border: "none", opacity: busy || loading ? 0.6 : 1 }}>📸 {busy ? "Saving…" : "Snapshot predictions"}</button>
        <select value={snapDate} onChange={e => setSnapDate(e.target.value)} style={{ ...sel, cursor: "pointer" }}>
          <option value="">Live (recompute now)</option>
          {snapDates.map(d => <option key={d} value={d}>Snapshot {d}</option>)}
        </select>
        <select value={cropF} onChange={e => setCropF(e.target.value)} style={{ ...sel, cursor: "pointer" }}>
          <option value="ALL">All crops</option><option value="CORN">Corn</option><option value="SOYBEANS">Soybeans</option>
        </select>
        <select value={stateF} onChange={e => { setStateF(e.target.value); setCountyF("ALL"); }} style={{ ...sel, cursor: "pointer" }}>
          <option value="ALL">All states</option>{stateOpts.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={countyF} onChange={e => setCountyF(e.target.value)} style={{ ...sel, cursor: "pointer" }}>
          <option value="ALL">All counties</option>{countyOpts.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {status && <span style={{ fontSize: 12, color: status.startsWith("📸") ? "#2d6a1a" : "#b00", fontWeight: 600 }}>{status}</span>}
        {!snapDate && <FreshAsOf date={wxThrough} label="observed weather" prefix="through" style={{ marginLeft: "auto" }} />}
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        {[["Predictions", String(view.length)],
          ["Scored vs actual", String(scored.length)],
          ["Model MAE", mae != null ? `${mae.toFixed(1)} bu` : "—"],
          ["Baseline MAE", baseMae != null ? `${baseMae.toFixed(1)} bu` : "—"],
        ].map(([l, v]) => (
          <div key={l} style={{ ...card, flex: isMobile ? "1 1 44%" : "0 0 auto", minWidth: isMobile ? 0 : 130 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#60735f" }}>{l}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1a2e1a", marginTop: 2 }}>{v}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ ...card, color: "#60735f" }}>Resolving each keyed field to its high-def weather cell and running the model…</div>
      ) : !view.length ? (
        <div style={{ ...card, color: "#60735f" }}>No {yr} keyed acres match the filters yet — this fills in as M13 pulls acreage reports.</div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 940 }}>
              <thead><tr style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em", background: "#f6faf6" }}>
                {COLS.map(([k, label, right]) => (
                  <th key={k} onClick={() => toggleSort(k)} style={{ textAlign: right ? "right" : "left", padding: "8px 12px", borderBottom: "1px solid #e3ebe3", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }}>
                    {label}{sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {view.map((r, i) => {
                  const err = (r.actual != null && r.predicted != null) ? r.predicted - r.actual : null;
                  return (
                    <tr key={r.fid + r.crop + r.county + i} style={{ borderTop: "1px solid #f0f4f0" }}>
                      <td style={{ padding: "7px 12px", color: "#1f3320", fontWeight: 600 }}>{nameById[r.fid] || <span style={{ color: "#677167" }}>(former)</span>}</td>
                      <td style={{ padding: "7px 12px", color: "#4a6e4a" }}>{cropTag(r.crop)} {r.crop === "SOYBEANS" ? "Soy" : "Corn"}</td>
                      <td style={{ padding: "7px 12px", color: "#60735f" }}>{r.county}{r.state ? <span style={{ color: "#bcc8bc" }}>, {r.state}</span> : ""}</td>
                      <td style={{ padding: "7px 12px", color: "#4a6e4a", whiteSpace: "nowrap" }}>{r.plantMin != null ? (r.plantMin === r.plantMax ? doyMD(r.plantMin) : `${doyMD(r.plantMin)}–${doyMD(r.plantMax)}`) : doyMD(r.plantDoy)}{r.nFields > 1 ? <span style={{ color: "#bcc8bc", fontSize: 10.5 }}> · {r.nFields} fld</span> : ""}</td>
                      <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#4a6e4a" }}>{r.acres != null ? r.acres.toLocaleString() : "—"}</td>
                      <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#4a6e4a" }}>{Math.round(r.baseline)} <span style={{ color: "#60735f", fontSize: 10 }}>APH</span></td>
                      <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: r.mf == null ? "#677167" : r.mf < 0.97 ? "#b1442e" : r.mf > 1.03 ? "#2d6a1a" : "#1a2e1a" }}>{r.mf != null ? `${r.mf > 1 ? "▲" : r.mf < 1 ? "▼" : "■"} ${Math.round(r.mf * 100)}%` : "—"}</td>
                      <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 800, color: "#1a2e1a" }}>{r.predicted != null ? Math.round(r.predicted) : "—"}</td>
                      <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: r.actual != null ? "#1f3320" : "#bcc8bc" }}>{r.actual != null ? Math.round(r.actual) : "—"}</td>
                      <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: err == null ? "#bcc8bc" : Math.abs(err) <= 10 ? "#2d6a1a" : Math.abs(err) <= 25 ? "#9a5a1a" : "#b1442e" }}>{err != null ? `${err >= 0 ? "+" : ""}${Math.round(err)}` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>)}
      {!armed && (
        <div style={{ ...card, color: "#54724f", fontSize: 13, lineHeight: 1.6 }}>
          Pick a farm above to score it, or <b>All farms</b> for the whole book.
          <div style={{ fontSize: 12, color: "#60735f", marginTop: 6 }}>
            Nothing has been read yet. The model is the same either way — a farm run is
            narrower, not simpler: the same official APH, the same per-field ~4&nbsp;km
            weather, the same scoring against actuals.
          </div>
        </div>
      )}
      <div style={{ fontSize: 10.5, color: "#677167", marginTop: 8, lineHeight: 1.5 }}>
        Each keyed field runs the mechanistic model on <b>its own ~4 km gridMET + MRMS-gauge-corrected cell</b> with the field’s <b>real planting date</b>, indexed vs that cell’s normal and projected with normal weather for the rest of the season, then acre-weighted to the grower. Predicted = <b>official grain APH</b> (crop_coverage) × index; growers with no grain APH on file are omitted. Backtested grower MAE: corn ~23 bu, soy ~8 bu — an internal scoring/tuning number, not farmer-facing. Snapshot through the season to watch it converge as observed weather replaces the forecast.
      </div>
    </div>
  );
}

// ── SETTLE LRP AEVs FROM RMA — admin trigger ─────────────────────────────────
// Moved here 2026-11-02 from the retired LRP page, unchanged: same endpoint, same
// query, same admin-only gate, same result line. Sync Log is where the pipelines
// live, and this is a manual push of one (rma-lrp-sync).
//
// Chunk size 15 per call — clears a slice of the backlog and PATCHes
// actual_end_value + payable_indemnity. Backlog was building because the daily
// cron was hitting 60s timeout on the 75-row queue. Each click of this button
// settles ~15 endorsements.
function RmaLrpSettleControl() {
  const [settling, setSettling] = useState(false);
  const [settleResult, setSettleResult] = useState(null);
  const triggerSettle = useCallback(async () => {
    setSettling(true);
    setSettleResult(null);
    try {
      const headers = getApiAuthHeaders();
      const res = await fetch("/api/rma-lrp-sync?skipAlerts=1", { method: "GET", headers });
      const j = await res.json().catch(() => ({}));
      setSettleResult({ ok: res.ok, ...j });
    } catch (e) {
      setSettleResult({ ok: false, error: e.message });
    }
    setSettling(false);
  }, []);
  if (!isCurrentUserAdmin()) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={triggerSettle} disabled={settling}
        title="Pulls today's RMA daily ZIP and PATCHes actual_end_value + payable_indemnity for up to 15 settled endorsements. Click multiple times if the backlog is large."
        style={{
          padding: "8px 14px", background: settling ? "#9ab39a" : "#2d6a2d", color: "#fff",
          border: "none", borderRadius: 7, fontWeight: 700, fontSize: 12,
          cursor: settling ? "default" : "pointer",
        }}>
        {settling ? "Settling…" : "🔄 Settle AEVs from RMA"}
      </button>
      {settleResult && (
        <div style={{
          marginTop: 8, padding: "8px 12px", borderRadius: 6, fontSize: 11.5,
          background: settleResult.ok ? "#ecfdf5" : "#fbeaea",
          border: `1px solid ${settleResult.ok ? "#bbf7d0" : "#f0c5c5"}`,
          color: settleResult.ok ? "#065f46" : "#a3271f",
          fontVariantNumeric: "tabular-nums",
        }}>
          {settleResult.ok ? (
            <>
              <b>{settleResult.applied || 0}</b> settled · <b>{settleResult.match_fail_count || 0}</b> match failures · <b>{settleResult.backlog_remaining ?? "?"}</b> still pending
              {settleResult.zips_fetched != null && <> · {settleResult.zips_fetched}/{settleResult.zips_attempted} ZIPs fetched in {Math.round((settleResult.elapsed_ms || 0)/1000)}s</>}
              {settleResult.match_fail_count > 0 && settleResult.match_fails_sample && settleResult.match_fails_sample.length > 0 && (
                <div style={{ marginTop: 4, color: "#7c5e00" }}>
                  First fail: <code>{settleResult.match_fails_sample[0].reason}</code>
                </div>
              )}
              {settleResult.backlog_remaining > 0 && <div style={{ marginTop: 4 }}>Click again to settle the next chunk.</div>}
            </>
          ) : (
            <>Error: {settleResult.error || `HTTP ${settleResult.status || '?'}`}</>
          )}
        </div>
      )}
    </div>
  );
}

function SyncLogScreen({ isMobile }) {
  const [changes,    setChanges]    = useState([]);
  const [importLogs, setImportLogs] = useState([]);
  const [syncRuns,   setSyncRuns]   = useState([]); // generic sync_runs (NASS/AMS/PPD/LRP)
  const [drpFreshIso, setDrpFreshIso] = useState(null);
  const [pipelineFresh, setPipelineFresh] = useState({}); // { nasscrops, nassdairy, amsmilk, amscattle, ppd, lrp }
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filterAIP,  setFilterAIP]  = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [days,       setDays]       = useState(1);
  const [hideReimports, setHideReimports] = useState(true);

  const CHANGE_TYPE_LABELS = {
    acres:          { label: "Acres Reported", color: "#2d6a1a", bg: "#eef6ec" },
    crop_coverage:  { label: "Crop Coverage",  color: "#1a4a7a", bg: "#eef2ff" },
    drp:            { label: "DRP",            color: "#1a5a8a", bg: "#e8f2ff" },
    lrp:            { label: "LRP",            color: "#6a4a1a", bg: "#fdf5e8" },
    prf:            { label: "PRF",            color: "#4a1a6a", bg: "#f5eeff" },
  };

  async function load() {
    setLoading(true);
    try {
      // sync_changes is loaded for max(days, 14) days so the Recent Sync Runs
      // table can attribute new-entry counts back to each import log (which
      // typically spans 5–10 days for 10 daily M13 + NAU runs). The activity
      // feed below applies the `days` filter on top of this wider window.
      const sinceDays = Math.max(days, 14);
      const since = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000).toISOString();
      const headers = { ...getAuthHeaders(), "Range-Unit": "items", "Range": "0-999", "Prefer": "count=none" };
      // Pipeline freshness: each source table exposes a fetched_at timestamptz.
      // Pull the latest row's fetched_at for each (filtered where needed) so the
      // pipeline cards can show real "X hours/days ago" instead of "Not tracked".
      const top1 = (path) => fetch(`${SUPABASE_URL}/rest/v1/${path}`, { headers }).then(r => r.ok ? r.json() : []).catch(() => []);
      // Full sync audit (2026-05-27): each data series we actually use gets
      // its own freshness fetch (was previously bundled under nassCrops /
      // nassDairy umbrellas that hid the per-series staleness).
      const [
        chRows, logRows, syncRunRows, drpRows,
        amsMilkRows, amsCattleRows, boxedBeefRows, ppdRows, lrpRows,
        noaaCpcRows, prfRollupRows, prfFinalRows, wxCountyRows, wnyGridRows, cornBasisRows, dmcRows,
        nassProgressRows, nassConditionsRows, nassMoistureRows, nassDroughtRows,
        nassDairyRows, nassCofRows, nassFeederRows,
        nassAcresRows, nassYieldRows, nassPriceRows,
        plantingSurveyRows, sobRows, box578Rows,
      ] = await Promise.all([
        // Activity / log
        fetch(`${SUPABASE_URL}/rest/v1/sync_changes?synced_at=gte.${since}&order=synced_at.desc&limit=1000`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${SUPABASE_URL}/rest/v1/m13_import_log?order=imported_at.desc&limit=50`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${SUPABASE_URL}/rest/v1/sync_runs?order=started_at.desc&limit=60`, { headers }).then(r => r.ok ? r.json() : []),
        // Live markets
        top1(`drp_board?select=price_date&order=price_date.desc&limit=1`),
        top1(`class_prices?select=fetched_at&order=fetched_at.desc.nullslast&limit=1`),       // AMS Class III/IV
        top1(`five_area_weekly?select=fetched_at&order=fetched_at.desc.nullslast&limit=1`),    // AMS 5-Area cattle
        top1(`boxed_beef_weekly?select=fetched_at&order=fetched_at.desc.nullslast&limit=1`),   // AMS boxed beef
        top1(`ppd?select=fetched_at&order=fetched_at.desc.nullslast&limit=1`),                 // AMS PPD/FMMO
        top1(`lrp_daily_rates?select=fetched_at&order=fetched_at.desc.nullslast&limit=1`),     // RMA LRP rates
        // PRF + yield-model substrate + WNY basis scrape
        top1(`prf_grid_daily_precip?select=synced_at&order=synced_at.desc.nullslast&limit=1`),
        top1(`prf_grid_indices?select=synced_at&estimated_index_value=not.is.null&order=synced_at.desc.nullslast&limit=1`),
        top1(`prf_grid_indices?select=published_at&is_final=eq.true&order=published_at.desc.nullslast&limit=1`),
        top1(`weather_county_daily?select=created_at&order=created_at.desc.nullslast&limit=1`),
        // WNY high-def field grid. Deliberately filtered to is_forecast=false: the
        // forecast leg of this same pipeline re-runs every 3h, so an unfiltered
        // freshness read would show "1h ago" and stay green even if the daily
        // OBSERVATION pull had been failing for a week.
        // ⚠ ORDERED BY obs_date, NOT synced_at. Sorting 7.8M rows by an unindexed
        // synced_at took 6.6 s — past the API timeout once this page fires its
        // thirty reads together, which turned every card red. The newest OBSERVED
        // day's sync stamp answers the same question (a failing daily pull stops
        // advancing it) and walks the obs_date index backwards: 0.05 s.
        top1(`wny_grid_weather_daily?select=synced_at&is_forecast=eq.false&order=obs_date.desc&limit=1`),
        top1(`sync_runs?select=completed_at&source=eq.corn-basis&order=completed_at.desc.nullslast&limit=1`),
        top1(`sync_runs?select=completed_at&source=eq.dmc-margins&order=completed_at.desc.nullslast&limit=1`),
        // NASS — Weekly Tue surveys (split out from the old umbrella)
        top1(`nass_data?select=fetched_at&crop=in.(CORN,SOYBEANS)&stat_type=in.(PCT_PLANTED,PCT_EMERGED)&order=fetched_at.desc.nullslast&limit=1`),
        top1(`nass_data?select=fetched_at&stat_type=eq.PCT_GOOD_EXCELLENT&order=fetched_at.desc.nullslast&limit=1`),
        top1(`nass_data?select=fetched_at&crop=eq.MOISTURE&order=fetched_at.desc.nullslast&limit=1`),
        top1(`nass_data?select=fetched_at&crop=eq.DROUGHT&order=fetched_at.desc.nullslast&limit=1`),
        // NASS — Monthly 22nd reports
        top1(`nass_data?select=fetched_at&crop=eq.MILK&order=fetched_at.desc.nullslast&limit=1`),
        top1(`nass_data?select=fetched_at&crop=eq.CATTLE&stat_type=in.(COF_INVENTORY,COF_PLACEMENTS,COF_MARKETINGS)&order=fetched_at.desc.nullslast&limit=1`),
        top1(`nass_data?select=fetched_at&stat_type=eq.FEEDER_CATTLE_PRICE&order=fetched_at.desc.nullslast&limit=1`),
        // NASS — Monthly-pulled / annual
        top1(`nass_data?select=fetched_at&stat_type=in.(ACRES_PLANTED,ACRES_HARVESTED,ACRES_HARVESTED_SILAGE)&order=fetched_at.desc.nullslast&limit=1`),
        top1(`nass_data?select=fetched_at&stat_type=in.(YIELD,YIELD_SILAGE)&order=fetched_at.desc.nullslast&limit=1`),
        top1(`nass_data?select=fetched_at&stat_type=eq.PRICE_RECEIVED&order=fetched_at.desc.nullslast&limit=1`),
        // Customer inputs + RMA SOB
        top1(`planting_progress?select=submitted_at&order=submitted_at.desc.nullslast&limit=1`),
        top1(`sob_data?select=fetched_at&order=fetched_at.desc.nullslast&limit=1`),
        // FSA-578 Box sync — manual sync_runs row (source='box-578')
        top1(`sync_runs?select=completed_at&source=eq.box-578&order=completed_at.desc.nullslast&limit=1`),
      ]);
      setChanges(Array.isArray(chRows) ? chRows : []);
      setImportLogs(Array.isArray(logRows) ? logRows : []);
      setSyncRuns(Array.isArray(syncRunRows) ? syncRunRows : []);
      if (Array.isArray(drpRows) && drpRows.length > 0 && drpRows[0].price_date) {
        // drp_daily_prices.price_date is a DATE, not a timestamp — treat as end of that day EST
        setDrpFreshIso(drpRows[0].price_date + "T17:00:00-04:00");
      } else {
        setDrpFreshIso(null);
      }
      // Generalized so non-fetched_at sources (synced_at / published_at /
      // created_at / completed_at) can use the same helper.
      const pickIso = (rows, field = "fetched_at") =>
        Array.isArray(rows) && rows[0]?.[field] ? rows[0][field] : null;
      setPipelineFresh({
        // Markets — each tracked individually now (5-Area and Boxed Beef no
        // longer combined since each has its own publish cadence we care about)
        amsmilk:        pickIso(amsMilkRows),
        amscattle:      pickIso(amsCattleRows),     // 5-Area
        amsboxedbeef:   pickIso(boxedBeefRows),
        ppd:            pickIso(ppdRows),
        lrp:            pickIso(lrpRows),
        // Weather / PRF
        noaacpc:        pickIso(noaaCpcRows,   "synced_at"),
        prfrollup:      pickIso(prfRollupRows, "synced_at"),
        prffinal:       pickIso(prfFinalRows,  "published_at"),
        wxcounty:       pickIso(wxCountyRows,  "created_at"),
        wnygrid:        pickIso(wnyGridRows,   "synced_at"),
        cornbasis:      pickIso(cornBasisRows, "completed_at"),
        dmc:            pickIso(dmcRows,       "completed_at"),
        // NASS — Weekly Tue
        nassprogress:   pickIso(nassProgressRows),
        nassconditions: pickIso(nassConditionsRows),
        nassmoisture:   pickIso(nassMoistureRows),
        nassdrought:    pickIso(nassDroughtRows),
        // NASS — Monthly 22nd
        nassdairy:      pickIso(nassDairyRows),
        nasscof:        pickIso(nassCofRows),
        nassfeeder:     pickIso(nassFeederRows),
        // NASS — Monthly 5th / annual
        nassacres:      pickIso(nassAcresRows),
        nassyield:      pickIso(nassYieldRows),
        nassprice:      pickIso(nassPriceRows),
        // Customer inputs + RMA SOB
        plantingsurvey: pickIso(plantingSurveyRows, "submitted_at"),
        sob:            pickIso(sobRows),
        box578:         pickIso(box578Rows, "completed_at"),
      });
    } catch(e) {
      console.error("SyncLog load error:", e);
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, [days]);

  // ─── PIPELINE HEALTH ─────────────────────────────────────────────────────
  // Derive health status for each known pipeline
  const pipelineHealth = useMemo(() => {
    // M13 pipelines — identify AIP from filename
    const rhRuns  = importLogs.filter(l => /SpartanNY|PASS|_RH|rain/i.test(l.filename||""));
    const nauRuns = importLogs.filter(l => /953616|NAU/i.test(l.filename||""));
    const latestRH  = rhRuns[0]  || null;
    const latestNAU = nauRuns[0] || null;

    // Daily pipeline: green < 30 business hrs, amber < 72 business hrs, red otherwise
    const dailyStatus = (iso) => {
      const h = businessHoursSince(iso);
      if (h < 30)  return { tier: "green", label: "Healthy" };
      if (h < 72)  return { tier: "amber", label: "Running late" };
      return { tier: "red", label: "Stale" };
    };

    // Weekly: green < 10d, amber < 14d, red otherwise
    const weeklyStatus = (iso) => {
      const d = daysSince(iso);
      if (d < 10) return { tier: "green", label: "Current" };
      if (d < 14) return { tier: "amber", label: "Running late" };
      return { tier: "red", label: "Stale" };
    };

    // Monthly: green < 40d, amber < 50d, red otherwise
    const monthlyStatus = (iso) => {
      const d = daysSince(iso);
      if (d < 40) return { tier: "green", label: "Current" };
      if (d < 50) return { tier: "amber", label: "Running late" };
      return { tier: "red", label: "Stale" };
    };

    // Annual / manual upload: green < 13mo, amber < 14mo, red otherwise. The RMA
    // SOB is an agency Excel upload (no cron), so the monthly threshold red-flags
    // it falsely at ~2 months — grade it on an annual cadence instead.
    const annualStatus = (iso) => {
      const d = daysSince(iso);
      if (d < 400) return { tier: "green", label: "Current" };
      if (d < 430) return { tier: "amber", label: "Refresh due" };
      return { tier: "red", label: "Stale" };
    };

    // Full audit (2026-05-27): every scheduled sync/pull is now its own card,
    // tagged with a `section` for grouped rendering below.
    const card = (section, key, label, desc, iso, statusFn, lastMeta = null, noData = "No data") => ({
      section, key, label, desc, lastIso: iso, lastMeta,
      status: iso ? statusFn(iso) : { tier: "red", label: noData },
      tracked: true,
    });
    const pipelines = [
      // ── CUSTOMER BOOK ──
      {
        section: "Customer Book", key: "rh", label: "R&H M13",
        desc: "Daily 6:00 AM ET · SFTP from secureftp.rainhail.com (4× retry pattern)",
        lastIso: latestRH?.imported_at,
        lastMeta: latestRH ? `${latestRH.policy_count || 0} policies · $${Math.round(latestRH.total_premium || 0).toLocaleString()}` : null,
        status: latestRH ? dailyStatus(latestRH.imported_at) : { tier: "red", label: "No runs recorded" },
        tracked: true,
      },
      {
        section: "Customer Book", key: "nau", label: "NAU M13",
        desc: "Daily 6:30 AM ET · SFTP from sftp.naucountry.com (4× retry pattern)",
        lastIso: latestNAU?.imported_at,
        lastMeta: latestNAU ? `${latestNAU.policy_count || 0} policies · $${Math.round(latestNAU.total_premium || 0).toLocaleString()}` : null,
        status: latestNAU ? dailyStatus(latestNAU.imported_at) : { tier: "red", label: "No runs recorded" },
        tracked: true,
      },
      card("Customer Book", "lrp",      "LRP Rates",                "Weekday after market close · RMA piggyback (4× retry)", pipelineFresh.lrp, dailyStatus),
      card("Customer Book", "drp",      "DRP Quotes",               "Daily after market close · Ever.Ag → Apps Script push", drpFreshIso, dailyStatus),
      card("Customer Book", "prffinal", "PRF Final Index (RMA)",    "Monthly ~18th · official RMA grid-index publication",   pipelineFresh.prffinal, monthlyStatus),
      card("Customer Book", "sob",      "RMA Summary of Business",  "Annual · manual Excel upload · agency-aggregated acres / liability / premium / indemnity", pipelineFresh.sob, annualStatus),

      // ── MARKETS ──
      card("Markets", "amsmilk",      "AMS Class III/IV Milk",   "Monthly ~7th · announced Class III + IV milk prices",        pipelineFresh.amsmilk,      monthlyStatus),
      card("Markets", "ppd",          "AMS PPD / FMMO",          "Monthly ~16th · uniform price + producer price differential", pipelineFresh.ppd,          monthlyStatus),
      card("Markets", "dmc",          "FSA DMC Margins",         "Monthly 1st + 16th · Dairy Margin Coverage margin scrape (FSA)", pipelineFresh.dmc,         monthlyStatus, null, "Never run"),
      card("Markets", "ams5area",     "AMS 5-Area Cattle",       "Weekly Tue · weighted average live-cattle price (LM_CT150)", pipelineFresh.amscattle,    weeklyStatus),
      card("Markets", "amsboxedbeef", "AMS Boxed Beef Cutout",   "Weekly Tue · choice + select cutout values (LM_XB459)",      pipelineFresh.amsboxedbeef, weeklyStatus),
      card("Markets", "cornbasis",    "WNY Energy Basis Scrape", "Weekday ~3:30 PM ET · corn cash bid + futures",              pipelineFresh.cornbasis,    dailyStatus, null, "Never run"),

      // ── NASS — WEEKLY SURVEYS ──
      card("NASS Weekly", "nassprogress",   "Planting Progress",       "Weekly Tue · CORN/SOY % planted + emerged (state-level)",              pipelineFresh.nassprogress,   weeklyStatus),
      card("NASS Weekly", "nassconditions", "Crop Conditions",         "Weekly Tue · % Good/Excellent ratings (state-level)",                  pipelineFresh.nassconditions, weeklyStatus),
      card("NASS Weekly", "nassmoisture",   "Soil Moisture",           "Weekly Tue · topsoil + subsoil moisture (very short → surplus)",       pipelineFresh.nassmoisture,   weeklyStatus),
      card("NASS Weekly", "nassdrought",    "Drought Monitor (USDM)",  "Weekly · US Drought Monitor severity composite",                       pipelineFresh.nassdrought,    weeklyStatus),

      // ── NASS — MONTHLY REPORTS ──
      card("NASS Monthly", "nassdairy",  "Dairy Production",      "Monthly 22nd · milk production, per cow, fat test, cow inventory", pipelineFresh.nassdairy,  monthlyStatus),
      card("NASS Monthly", "nasscof",    "Cattle on Feed",        "Monthly 22nd · COF inventory / placements / marketings",          pipelineFresh.nasscof,    monthlyStatus),
      card("NASS Monthly", "nassfeeder", "Feeder Cattle Price",   "Monthly 22nd · NASS national $/cwt (CME index proxy)",            pipelineFresh.nassfeeder, monthlyStatus),
      card("NASS Monthly", "nassprice",  "Prices Received (NASS)","Monthly · prices received for CORN / SOY / WHEAT / MILK",         pipelineFresh.nassprice,  monthlyStatus),

      // ── NASS — ANNUAL ──
      card("NASS Annual", "nassacres", "County Acres",  "Annual ~late June · planted + harvested acres (CORN/SOY/WHEAT/HAY)", pipelineFresh.nassacres, monthlyStatus),
      card("NASS Annual", "nassyield", "County Yields", "Annual ~Feb · county-level corn / soy / wheat + silage yields",       pipelineFresh.nassyield, monthlyStatus),

      // ── WEATHER ──
      card("Weather", "noaacpc",   "NOAA CPC Rainfall",          "Daily ~11:30 AM ET · per-grid precip (PRF source data)",         pipelineFresh.noaacpc,   dailyStatus),
      card("Weather", "prfrollup", "PRF Indices Rollup",         "Daily after NOAA CPC · running %-of-normal per grid+interval",   pipelineFresh.prfrollup, dailyStatus),
      card("Weather", "wxcounty",  "County Weather + Forecast",  "Daily · observed past + 14-day forecast (yield-model input)",    pipelineFresh.wxcounty,  dailyStatus),
      card("Weather", "wnygrid",   "WNY Field Grid (gridMET+MRMS)", "Daily 9:00 AM ET observations + forecast every 3h · 1,271 cells + per-field rows (Field Pulse source)", pipelineFresh.wnygrid, dailyStatus),

      // ── CUSTOMER INPUTS ──
      card("Customer Inputs", "plantingsurvey", "Farmer Planting Survey", "On-demand · farmer-submitted acres planted (planting-survey.html)", pipelineFresh.plantingsurvey, weeklyStatus, null, "No submissions"),
      card("Customer Inputs", "box578",         "FSA-578 (Box Sync)",     "Weekday 8a/11a/2p/5p ET + manual · scan Box → extract → match → email Mandy", pipelineFresh.box578,         dailyStatus, null, "Never run"),
    ];

    return pipelines;
  }, [importLogs, drpFreshIso, pipelineFresh]);

  // ─── NEW-ENTRY COUNTS PER IMPORT ─────────────────────────────────────────
  // Attribute sync_changes rows back to the m13_import_log they ran during.
  // For each log at imported_at = T (logs sorted desc), changes belong to it
  // when synced_at >= T AND synced_at < (next-newer log's imported_at).
  // This swaps the meaningless "total file size" count in Recent Sync Runs
  // for the actually-useful "what's new" count.
  const newCountByLog = useMemo(() => {
    if (!importLogs.length) return {};
    // Build sorted ascending boundaries so we can bucket each change quickly
    const logsAsc = [...importLogs].sort((a, b) => new Date(a.imported_at) - new Date(b.imported_at));
    const out = {};
    for (const c of changes) {
      if (!c.synced_at) continue;
      const t = new Date(c.synced_at).getTime();
      // find the most-recent log whose imported_at <= t
      let chosen = null;
      for (let i = logsAsc.length - 1; i >= 0; i--) {
        const lt = new Date(logsAsc[i].imported_at).getTime();
        if (lt <= t) { chosen = logsAsc[i]; break; }
      }
      if (!chosen) continue;
      // also require AIP match: R&H changes go to R&H imports, NAU to NAU
      const logIsNau = /953616|NAU/i.test(chosen.filename || "");
      const logAip = logIsNau ? "NAU" : "R&H";
      if ((c.aip || "") !== logAip) continue;
      out[chosen.id] = (out[chosen.id] || 0) + 1;
    }
    return out;
  }, [importLogs, changes]);

  // Whether the sync_changes window covers each log; older logs may be outside
  // the 14-day load window and shouldn't show "0 new" (which is misleading).
  const oldestChangeIso = changes.length > 0 ? changes[changes.length - 1].synced_at : null;

  // ─── ACTIVITY FEED ───────────────────────────────────────────────────────
  // Show only TRUE new entries: rows in the display window that have no prior
  // matching (policy_number, change_type, detail) in the full 14-day window.
  // The previous version deduped only within the display window, which meant
  // "Today" could never compare against yesterday — every daily re-import of
  // already-reported acres showed up as if it were brand new.
  const { filtered, reimportCount } = useMemo(() => {
    // Apply AIP/type/search filters across the FULL window before slicing by date,
    // so the prior-key set we build below is also subject to those filters.
    const applyFilters = (lst) => {
      if (filterAIP  !== "all") lst = lst.filter(c => c.aip === filterAIP);
      if (filterType !== "all") lst = lst.filter(c => c.change_type === filterType);
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        lst = lst.filter(c =>
          (c.farmer_name||"").toLowerCase().includes(q) ||
          (c.policy_number||"").toLowerCase().includes(q) ||
          (c.detail||"").toLowerCase().includes(q)
        );
      }
      return lst;
    };
    const all = applyFilters(changes);
    // Calendar-day boundary for the display window — "Today" means since
    // midnight today, not the rolling last 24 hours (which would catch
    // yesterday's morning import when viewed early today).
    const cutoff = (() => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - (days - 1));
      return d.getTime();
    })();
    const inWindowAll = all.filter(c => !c.synced_at || new Date(c.synced_at).getTime() >= cutoff);

    if (!hideReimports) {
      const sorted = [...inWindowAll].sort((a,b) => new Date(b.synced_at || 0) - new Date(a.synced_at || 0));
      return { filtered: sorted, reimportCount: 0 };
    }

    // Dedupe across the FULL 14-day window: keep only the OLDEST occurrence
    // per signature, then filter that to the display window. The signature
    // is NORMALIZED so wording tweaks don't break dedup — extracts the
    // numeric total + crop list for acres entries, count for lrp/drp,
    // acres for prf. Without this, the day we flipped acres wording from
    // "WHEAT · COUNTY · 1048 ac" to "+524 ac reported · now 1048 ac total
    // · WHEAT" produced 152 phantom-new wheat entries even though the
    // total was unchanged.
    const dedupeKey = (c) => {
      const detail = String(c.detail || "");
      if (c.change_type === 'acres') {
        const m = detail.match(/now ([\d.]+) ac total/) || detail.match(/(\d[\d.]*) ac\b/);
        const total = m ? Math.round(parseFloat(m[1])) : 'unk';
        const cropMatches = (detail.match(/[A-Z]{4,}(?:\s[A-Z]+)*/g) || [])
          .filter(s => !["WAS","NOW","AC","AND","OR"].includes(s));
        const crops = [...new Set(cropMatches)].sort().join('+');
        return `${c.policy_number}|acres|${total}|${crops}`;
      }
      if (c.change_type === 'lrp' || c.change_type === 'drp') {
        const m = detail.match(/\((\d+) total\)/) || detail.match(/(\d+) (?:LRP|DRP|new)/);
        const total = m ? parseInt(m[1]) : 'unk';
        return `${c.policy_number}|${c.change_type}|${total}`;
      }
      if (c.change_type === 'prf') {
        const m = detail.match(/now ([\d.]+) ac/) || detail.match(/(\d[\d.]*) ac\b/);
        const total = m ? Math.round(parseFloat(m[1])) : 'unk';
        return `${c.policy_number}|prf|${total}`;
      }
      return `${c.policy_number}|${c.change_type}|${c.detail}`;
    };
    const ascByTime = [...all].sort((a,b) => new Date(a.synced_at || 0) - new Date(b.synced_at || 0));
    const seen = new Set();
    const firsts = [];
    for (const c of ascByTime) {
      const k = dedupeKey(c);
      if (!seen.has(k)) { seen.add(k); firsts.push(c); }
    }
    const trueNew = firsts.filter(c => !c.synced_at || new Date(c.synced_at).getTime() >= cutoff);
    trueNew.sort((a,b) => new Date(b.synced_at || 0) - new Date(a.synced_at || 0));
    return { filtered: trueNew, reimportCount: inWindowAll.length - trueNew.length };
  }, [changes, filterAIP, filterType, search, hideReimports, days]);

  // Group by sync date (day)
  const grouped = useMemo(() => {
    const groups = {};
    for (const c of filtered) {
      const day = (c.synced_at||"").slice(0,10);
      if (!groups[day]) groups[day] = [];
      groups[day].push(c);
    }
    return Object.entries(groups).sort((a,b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  // ─── FORMATTERS ──────────────────────────────────────────────────────────
  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit", hour12:true });
  };
  const fmtRelative = (iso) => {
    if (!iso) return "—";
    const h = wallHoursSince(iso);
    if (h < 1)   return "just now";
    if (h < 24)  return `${Math.floor(h)}h ago`;
    const d = h / 24;
    if (d < 7)   return `${Math.floor(d)}d ago`;
    if (d < 30)  return `${Math.floor(d / 7)}w ago`;
    return `${Math.floor(d / 30)}mo ago`;
  };
  const fmtDay = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso + "T12:00:00");
    const today = new Date(); const yesterday = new Date(today); yesterday.setDate(today.getDate()-1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-US", { weekday:"long", month:"short", day:"numeric" });
  };

  // ─── STATUS DOT ──────────────────────────────────────────────────────────
  const STATUS_COLORS = {
    green: { dot: "#2d8a3a", bg: "#e8f6e8", border: "#b8d8b0", text: "#1a5a1a" },
    amber: { dot: "#d08a1a", bg: "#fdf5e8", border: "#f0d0a0", text: "#8a5a00" },
    red:   { dot: "#c0392b", bg: "#fdecea", border: "#f0c0c0", text: "#8a1a1a" },
    grey:  { dot: "#9a9a9a", bg: "#f2f2f2", border: "#d8d8d8", text: "#5a5a5a" },
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      {/* ─── HEADER ─── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ ...uiPageTitle(isMobile), marginBottom: 4 }}>Sync Log</h1>
        <div style={{ fontSize: 12, color: "#60735f" }}>
          Pipeline health + activity feed for all data syncs
        </div>
      </div>

      {/* ─── SECTION 1: PIPELINE HEALTH ─── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", letterSpacing: "0.02em" }}>
            ⚡ Pipeline Health
          </div>
          <button onClick={load} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #dde5dd", background: "#fff", color: "#2d4a2d", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            ↺ Refresh
          </button>
        </div>
        <RmaLrpSettleControl />
        {/* Until the FIRST read lands, every card would say "never" in red — a
            page full of failures that is really a page still loading. A refresh
            keeps the last good cards on screen. */}
        {loading && !importLogs.length && !syncRuns.length ? (
          <div style={{ padding: "18px 4px", color: "#60735f", fontSize: 13 }}>
            <UiSpinner />Checking every pipeline…
          </div>
        ) : (() => {
          // Group by section preserving the order in which sections first appear.
          const order = [];
          const groups = {};
          for (const p of pipelineHealth) {
            const s = p.section || "Other";
            if (!groups[s]) { groups[s] = []; order.push(s); }
            groups[s].push(p);
          }
          const renderCard = (p) => {
            const c = STATUS_COLORS[p.status.tier];
            return (
              <div key={p.key} style={{
                padding: "12px 14px",
                borderRadius: 10,
                border: `1.5px solid ${c.border}`,
                background: c.bg,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: c.dot, display: "inline-block", flexShrink: 0 }} />
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#1a2e1a", flex: 1 }}>{p.label}</div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: c.text, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    {p.status.label}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#60735f", marginBottom: 4 }}>
                  {p.desc}
                </div>
                {p.tracked ? (
                  <>
                    <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 12, color: "#1a2e1a", fontWeight: 700 }}>
                      {p.lastIso ? fmtRelative(p.lastIso) : "never"}
                    </div>
                    {p.lastIso && (
                      <div style={{ fontSize: 10, color: "#9aa09a", marginTop: 2 }}>
                        {fmtTime(p.lastIso)}{p.lastMeta ? ` · ${p.lastMeta}` : ""}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 10, color: "#9aa09a", fontStyle: "italic" }}>
                    {p.lastMeta}
                  </div>
                )}
              </div>
            );
          };
          return order.map((section) => (
            <div key={section} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#60735f", letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 6 }}>
                {section}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
                {groups[section].map(renderCard)}
              </div>
            </div>
          ));
        })()}
      </div>

      {/* ─── SECTION 1.5: HISTORICAL BACKFILL ADMIN ─── */}
      <ForceSyncNow isMobile={isMobile} />
      <HistoricalBackfillAdmin isMobile={isMobile} />
      <WeatherBackfillAdmin isMobile={isMobile} />
      <MrmsBackfillAdmin isMobile={isMobile} />

      {/* ─── SECTION 2: RECENT SYNC RUNS ─── */}
      {importLogs.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", letterSpacing: "0.02em", marginBottom: 10 }}>
            🕐 Recent Sync Runs
          </div>
          <div style={{ fontSize: 10, color: "#9aa09a", marginBottom: 6 }}>
            Counts only what was new in each run — rows added to <code>sync_changes</code> while that import was running. File totals are shown in parentheses for reference.
          </div>
          <div style={{ border: "1px solid #e4ece4", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto auto auto auto", gap: 0, fontSize: 11, fontWeight: 700, color: "#60735f", background: "#f5f7f5", padding: "8px 14px", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              <div style={{ minWidth: 50 }}>AIP</div>
              <div>When</div>
              <div style={{ textAlign: "right", paddingRight: 12 }}>New entries</div>
              <div style={{ textAlign: "right", paddingRight: 12 }}>Premium</div>
              <div style={{ textAlign: "right", paddingRight: 12 }}>Duration</div>
              <div style={{ textAlign: "right", minWidth: 60 }}>Status</div>
            </div>
            {importLogs.slice(0, 10).map((log, i) => {
              const aip = /953616|NAU/i.test(log.filename||"") ? "NAU" : /SpartanNY|PASS|_RH|rain/i.test(log.filename||"") ? "R&H" : "—";
              const ok = log.status === "success";
              const newCount = newCountByLog[log.id] || 0;
              // Detect whether this log falls outside the loaded sync_changes window
              // (in which case "0 new" would be misleading — we just don't have data).
              const outsideWindow = oldestChangeIso && new Date(log.imported_at) < new Date(oldestChangeIso);
              return (
                <div key={log.id || i} style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto auto auto auto",
                  gap: 0,
                  padding: "9px 14px",
                  borderTop: "1px solid #f0f4f0",
                  background: i % 2 === 0 ? "#fff" : "#fafcfa",
                  fontSize: 12,
                  alignItems: "center",
                }}>
                  <div style={{ minWidth: 50 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: aip === "NAU" ? "#e8f0ff" : "#eef6ec", color: aip === "NAU" ? "#1a3a7a" : "#1a5a1a", letterSpacing: "0.05em" }}>
                      {aip}
                    </span>
                  </div>
                  <div style={{ color: "#1a2e1a" }}>
                    <span style={{ fontWeight: 700 }}>{fmtRelative(log.imported_at)}</span>
                    <span style={{ color: "#9aa09a", marginLeft: 8, fontSize: 11 }}>{fmtTime(log.imported_at)}</span>
                  </div>
                  <div style={{ textAlign: "right", paddingRight: 12, fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>
                    {outsideWindow ? (
                      <span style={{ color: "#bbb" }} title="Run is outside the 14-day sync_changes window we load on this page">—</span>
                    ) : (
                      <>
                        <span style={{ fontWeight: 800, color: newCount > 0 ? "#2d6a1a" : "#60735f" }}>{newCount}</span>
                        <span style={{ color: "#9aa09a", fontSize: 10, marginLeft: 4 }}>({log.policy_count || 0})</span>
                      </>
                    )}
                  </div>
                  <div style={{ textAlign: "right", paddingRight: 12, fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>
                    ${Math.round(log.total_premium || 0).toLocaleString()}
                  </div>
                  <div style={{ textAlign: "right", paddingRight: 12, fontVariantNumeric: "tabular-nums", color: "#60735f", fontSize: 11 }}>
                    {log.duration_seconds ? `${Math.round(log.duration_seconds)}s` : "—"}
                  </div>
                  <div style={{ textAlign: "right", minWidth: 60 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: ok ? "#2d6a1a" : "#c0392b" }}>
                      {ok ? "✓" : "⚠"} {ok ? "OK" : log.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── SECTION 2b: NASS / AMS / PPD / LRP SYNC RUNS ─── */}
      {!loading && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", letterSpacing: "0.02em", marginBottom: 8 }}>
            📡 NASS / AMS / PPD / LRP Sync Runs
          </div>
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e0eae0", overflow: "hidden" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr auto auto" : "120px 1fr auto auto auto auto",
              padding: "8px 14px",
              background: "#f4f8f4",
              borderBottom: "1px solid #e0eae0",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: "#5a7a5a",
              textTransform: "uppercase",
            }}>
              <div>Source</div>
              {!isMobile && <div>When</div>}
              {!isMobile && <div style={{ textAlign: "right" }}>Trigger</div>}
              <div style={{ textAlign: "right" }}>Rows</div>
              {!isMobile && <div style={{ textAlign: "right" }}>Duration</div>}
              <div style={{ textAlign: "right" }}>Status</div>
            </div>
            {syncRuns.length === 0 ? (
              <div style={{ padding: "20px 14px", textAlign: "center", color: "#9aa595", fontSize: 12, fontStyle: "italic" }}>
                No runs logged yet. NASS/AMS/PPD/LRP runs will appear here after the next cron fires.
              </div>
            ) : (
              syncRuns.slice(0, 30).map((r, i) => {
                const source = r.source || "—";
                // Color by source family
                const family = source.startsWith("nass") ? "nass"
                            : source.startsWith("ams")  ? "ams"
                            : source.startsWith("ppd")  ? "ppd"
                            : source.startsWith("rma-lrp") ? "lrp" : "other";
                const familyTone = {
                  nass:  { bg: "#eef4ff", fg: "#1a3a7a" },
                  ams:   { bg: "#fef6e8", fg: "#7a4a00" },
                  ppd:   { bg: "#f4ecff", fg: "#5a2a8a" },
                  lrp:   { bg: "#eaf4e4", fg: "#2d5a10" },
                  other: { bg: "#f4f4f4", fg: "#5a5a5a" },
                }[family];
                // A run that is still 'running' long after it started did not
                // finish — the function was killed before its terminal write.
                // Shown as STALLED, not as in-flight: rendering it identical to
                // a live run is how the LRP sync died on 49 consecutive runs
                // over six business days without the page ever looking wrong.
                // Threshold is well past the longest maxDuration in the repo
                // (300s), so a genuinely running sync is never mislabelled.
                const stalled = r.status === "running" && r.started_at &&
                  (Date.now() - new Date(r.started_at).getTime()) > 20 * 60 * 1000;
                const status = stalled ? "stalled" : (r.status || "—");
                const statusTone = status === "success" ? { bg: "#eaf4e4", fg: "#2d5a10", icon: "✓" }
                                : status === "empty"   ? { bg: "#fef4e8", fg: "#a06020", icon: "○" }
                                : status === "skipped" ? { bg: "#f4f4f4", fg: "#7a7a7a", icon: "↻" }
                                : status === "partial" ? { bg: "#fef4e8", fg: "#a06020", icon: "◐" }
                                : status === "error"   ? { bg: "#fde4e4", fg: "#a02020", icon: "⚠" }
                                : status === "stalled" ? { bg: "#fde4e4", fg: "#a02020", icon: "⛔" }
                                : status === "running" ? { bg: "#eef4ff", fg: "#1a3a7a", icon: "⏳" }
                                : { bg: "#f4f4f4", fg: "#7a7a7a", icon: "—" };
                return (
                  <div key={r.id || i} style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr auto auto" : "120px 1fr auto auto auto auto",
                    gap: 8,
                    padding: "9px 14px",
                    borderTop: i === 0 ? "none" : "1px solid #f0f4f0",
                    background: i % 2 === 0 ? "#fff" : "#fafcfa",
                    fontSize: 12,
                    alignItems: "center",
                  }}>
                    <div>
                      <span style={{ display:"inline-block", fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: familyTone.bg, color: familyTone.fg, letterSpacing: "0.04em" }}>
                        {source}
                      </span>
                    </div>
                    {!isMobile && (
                      <div style={{ color: "#1a2e1a" }}>
                        <span style={{ fontWeight: 700 }}>{fmtRelative(r.started_at)}</span>
                        <span style={{ color: "#9aa09a", marginLeft: 8, fontSize: 11 }}>{fmtTime(r.started_at)}</span>
                      </div>
                    )}
                    {!isMobile && (
                      <div style={{ textAlign: "right", fontSize: 11, color: "#60735f", fontStyle: r.trigger === "cron" ? "normal" : "italic" }}>
                        {r.trigger || "—"}
                      </div>
                    )}
                    <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: r.rows_upserted > 0 ? "#1a2e1a" : "#9aa595", fontWeight: r.rows_upserted > 0 ? 700 : 400 }}>
                      {r.rows_upserted != null ? r.rows_upserted.toLocaleString() : "—"}
                    </div>
                    {!isMobile && (
                      <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#60735f", fontSize: 11 }}>
                        {r.duration_seconds != null ? `${Math.round(r.duration_seconds)}s` : "—"}
                      </div>
                    )}
                    <div style={{ textAlign: "right", minWidth: 70 }} title={r.error_message || ""}>
                      <span style={{ display:"inline-block", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: statusTone.bg, color: statusTone.fg, letterSpacing: "0.04em" }}>
                        {statusTone.icon} {status}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div style={{ fontSize: 10, color: "#9aa595", marginTop: 6, fontStyle: "italic" }}>
            Hover a row's status badge to see error details. Empty = ran cleanly but wrote 0 rows. Skipped = idempotent retry no-op.
          </div>
        </div>
      )}

      {/* ─── SECTION 3: ACTIVITY FEED ─── */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", letterSpacing: "0.02em" }}>
            📝 Activity Feed
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#5a7a5a", cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={hideReimports} onChange={e => setHideReimports(e.target.checked)} />
            Hide re-imports
          </label>
        </div>

        {/* Search + filters */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search farmer, policy, detail..."
            style={{ flex: 1, minWidth: 180, padding: "8px 12px", border: "1px solid #dde5dd", borderRadius: 8, fontSize: 13, outline: "none" }} />
          <select value={days} onChange={e => setDays(Number(e.target.value))}
            style={{ padding: "8px 11px", border: "1px solid #dde5dd", borderRadius: 8, fontSize: 12, color: "#1a2e1a", background: "#fff", cursor: "pointer" }}>
            <option value={1}>Today</option>
            <option value={2}>Last 2 days</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <select value={filterAIP} onChange={e => setFilterAIP(e.target.value)}
            style={{ padding: "8px 11px", border: "1px solid #dde5dd", borderRadius: 8, fontSize: 12, color: "#1a2e1a", background: "#fff", cursor: "pointer" }}>
            <option value="all">All AIPs</option>
            <option value="NAU">NAU</option>
            <option value="R&H">R&H</option>
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            style={{ padding: "8px 11px", border: "1px solid #dde5dd", borderRadius: 8, fontSize: 12, color: "#1a2e1a", background: "#fff", cursor: "pointer" }}>
            <option value="all">All types</option>
            <option value="acres">Acres Reported</option>
            <option value="crop_coverage">Crop Coverage</option>
            <option value="drp">DRP</option>
            <option value="lrp">LRP</option>
            <option value="prf">PRF</option>
          </select>
        </div>

        {/* Summary */}
        <div style={{ fontSize: 11, color: "#60735f", marginBottom: 10, fontWeight: 600 }}>
          {loading
            ? "Loading..."
            : `${filtered.length} record${filtered.length !== 1 ? "s" : ""} across ${grouped.length} day${grouped.length !== 1 ? "s" : ""}${hideReimports && reimportCount > 0 ? ` · ${reimportCount} re-import${reimportCount !== 1 ? "s" : ""} hidden` : ""}`}
        </div>

        {/* Feed */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#60735f", fontSize: 14 }}>Loading activity...</div>
        ) : grouped.length === 0 ? (
          <Card style={{ textAlign: "center", padding: "40px 24px", border: "1px dashed #c8d8c8" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{hideReimports && reimportCount > 0 ? "✅" : "📭"}</div>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#1a2e1a", marginBottom: 4 }}>
              {hideReimports && reimportCount > 0 ? "No new changes" : "No activity in this window"}
            </div>
            <div style={{ color: "#60735f", fontSize: 12 }}>
              {hideReimports && reimportCount > 0
                ? `${reimportCount} re-import${reimportCount !== 1 ? "s" : ""} hidden. Uncheck "Hide re-imports" to see all.`
                : "Sync activity will appear here as data comes in."}
            </div>
          </Card>
        ) : (
          grouped.map(([day, rows]) => {
            return (
              <div key={day} style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "#1a2e1a" }}>{fmtDay(day)}</div>
                  <div style={{ fontSize: 10, color: "#9a9a9a", fontVariantNumeric: "tabular-nums" }}>{day}</div>
                  <div style={{ fontSize: 11, color: "#60735f", marginLeft: "auto" }}>
                    {rows.length} record{rows.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div style={{ border: "1px solid #e4ece4", borderRadius: 10, overflow: "hidden" }}>
                  {rows.map((c, i) => {
                    const typeInfo = CHANGE_TYPE_LABELS[c.change_type] || { label: c.change_type, color: "#555", bg: "#f5f5f5" };
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: i < rows.length - 1 ? "1px solid #f0f4f0" : "none", background: i % 2 === 0 ? "#fff" : "#fafcfa" }}>
                        <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 5, background: c.aip === "NAU" ? "#e8f0ff" : "#eef6ec", color: c.aip === "NAU" ? "#1a3a7a" : "#1a5a1a", flexShrink: 0, letterSpacing: "0.05em" }}>
                          {c.aip}
                        </span>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#1a2e1a", minWidth: 160, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c.farmer_name}
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, background: typeInfo.bg, color: typeInfo.color, flexShrink: 0, whiteSpace: "nowrap" }}>
                          {typeInfo.label}
                        </span>
                        <div style={{ fontSize: 11, color: "#5a7a5a", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c.detail}
                        </div>
                        <div style={{ fontSize: 10, color: "#bbb", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                          {new Date(c.synced_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COVERAGE ADVISOR — ARC/PLC + SCO/ECO trigger tracking, side by side, per county.
// Feed A (ARC/PLC): arc_plc_reference + arc_co_county (FSA files). Feed B (SCO/ECO):
// sco_eco_county view (RMA area yields + band payout %). NY-only for v1.
// Two clocks: ARC/PLC settle on the MYA (~Oct); SCO/ECO on the harvest price (~Jun,
// discovered corn/soy=Oct, winter wheat=Aug). ARC actual yield (FSA) ≠ SCO/ECO
// actual yield (RMA area) — shown as separate lines on purpose.
// ═══════════════════════════════════════════════════════════════════════════
// Price windows (month 0-idx). SCO/ECO revenue plans discover TWO prices per crop year:
//   projected price (spring guarantee) — corn/soy = Feb, winter wheat = Aug of prior year (pjOff -1)
//   harvest price (actual)            — corn/soy = Oct, winter wheat = Aug of the crop year
// Each is a MONTH-LONG averaging window (daily futures avg), not a single-day event.
// ctr* = the CME new-crop contract RMA averages for the projected/harvest price
// (corn Dec, soybeans Nov, SRW wheat Sep) — pulled live from futures_settles_daily
// (the grain marketing feed) as a forward outlook / in-window running average.
const CA_CROPS = {
  CORN:     { label:"Corn",     myS:[8,1], myE:[7,31], mySlbl:"Sep–Aug",
    pjS:[1,1], pjE:[1,29], pjMo:"Feb", pjOff:0, pxS:[9,1], pxE:[9,31], pxMo:"Oct", commodity:"corn",     ctrMo:12, ctrLbl:"Dec" },
  SOYBEANS: { label:"Soybeans", myS:[8,1], myE:[7,31], mySlbl:"Sep–Aug",
    pjS:[1,1], pjE:[1,29], pjMo:"Feb", pjOff:0, pxS:[9,1], pxE:[9,31], pxMo:"Oct", commodity:"soybeans", ctrMo:11, ctrLbl:"Nov" },
  // Wheat's windows are the CEPP_NY ones (months here are JS 0-indexed): the guarantee
  // is discovered Aug 15 – Sep 14 of the year BEFORE the crop year, and the harvest price
  // over JULY of the crop year — not August. Carrying August here left the card reporting
  // "harvest tracking" through the first week of August on a price RMA had already set.
  WHEAT:    { label:"Wheat",    myS:[5,1], myE:[4,31], mySlbl:"Jun–May",
    pjS:[7,15], pjE:[8,14], pjMo:"Aug", pjOff:-1, pxS:[6,1], pxE:[6,31], pxMo:"Jul", commodity:"wheat",    ctrMo:9,  ctrLbl:"Sep" },
};
const CA_ORDER = ["CORN","SOYBEANS","WHEAT"];

// ── RMA Commodity Exchange Price Provisions, NEW YORK ────────────────────────
// Read off the actual CEPP tables (2026-07-19), not assumed — the windows are per-STATE and
// they do differ (Michigan corn harvest is Nov 1–30 where NY is Oct 1–31).
//
// The pairing that trips everyone up: BOTH the projected and the harvest price come off the
// SAME contract. Wheat's guarantee is set the fall BEFORE it's planted (yearOff -1) and its
// harvest price is read the following July; corn/soy set both inside the crop year.
//   projected = the GUARANTEE.  harvest = what the crop is valued at when RP settles.
// type/practice MUST be pinned or the price is meaningless: RMA publishes a separate price for
// every type AND practice, and they are not remotely comparable. 2026 NY, per bushel unless
// noted:  corn GRAIN (016) $4.62  ·  corn SILAGE (026) $38.25 per TON  ·  organic corn (713)
// $7.30  ·  wheat (011/003) $5.76  ·  organic wheat $11.16  ·  soybeans (997/003) $11.09.
// Grab the wrong row and the card reads a silage per-ton price as a bushel guarantee.
// Practice 003 = conventional non-irrigated, which is the book's default.
// `ard` = ACREAGE REPORTING DATE, read from ADM A00200 field 30 (not assumed). It decides
// whether a zero-acre row means "still coming in" or "didn't plant it" — wheat's 2026 date was
// 2025-11-15, so by summer its book is settled and a pending count would be a lie; corn/soy
// report 07-15 of the crop year, so through the summer their counts are genuinely filling in.
const CEPP_NY = {
  WHEAT:    { commodity:"wheat",    code:"0011", type:"011", practice:"003", unit:"bu", contractMo:9,  contractLbl:"Sep",
              proj:{ from:[8,15], to:[9,14], yearOff:-1 }, harv:{ from:[7,1],  to:[7,31],  yearOff:0 },
              ard:{ md:[11,15], yearOff:-1 } },
  CORN:     { commodity:"corn",     code:"0041", type:"016", practice:"003", unit:"bu", contractMo:12, contractLbl:"Dec",
              proj:{ from:[2,1],  to:[2,28], yearOff:0  }, harv:{ from:[10,1], to:[10,31], yearOff:0 },
              ard:{ md:[7,15],  yearOff:0  } },
  SOYBEANS: { commodity:"soybeans", code:"0081", type:"997", practice:"003", unit:"bu", contractMo:11, contractLbl:"Nov",
              proj:{ from:[2,1],  to:[2,28], yearOff:0  }, harv:{ from:[10,1], to:[10,31], yearOff:0 },
              ard:{ md:[7,15],  yearOff:0  } },
};
const cepDate = (y, md) => `${y}-${String(md[0]).padStart(2,"0")}-${String(md[1]).padStart(2,"0")}`;
const cepContract = (spec, cropYear) => `${cropYear}-${String(spec.contractMo).padStart(2,"0")}-01`;

// Where a discovery window sits relative to today, plus the running average so far.
// settles = [{settle_date, settle_price}] for the right contract, ascending.
function cepWindow(spec, win, cropYear, todayISO, settles) {
  const y = cropYear + win.yearOff;
  const from = cepDate(y, win.from), to = cepDate(y, win.to);
  const inWin = (settles || []).filter(r => r.settle_date >= from && r.settle_date <= to);
  const avg = inWin.length ? Math.round(inWin.reduce((s,r) => s + (+r.settle_price), 0) / inWin.length * 100) / 100 : null;
  const state = todayISO < from ? "before" : todayISO > to ? "closed" : "open";
  const days = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000);
  // Progress = elapsed share of the WINDOW, not a guess at how many trading days it holds.
  const pct = state === "open" ? Math.max(0, Math.min(100, Math.round(days(from, todayISO) / Math.max(1, days(from, to)) * 100))) : null;
  return { from, to, state, avg, n: inWin.length, pct,
           daysUntil: state === "before" ? days(todayISO, from) : null,
           daysLeft:  state === "open"   ? days(todayISO, to)   : null };
}

// ── Coverage Advisor landing dashboard ──────────────────────────────────────
// Two cards, both scoped to the crop tab you're on:
//   1. PRICE & GUARANTEE TRACKER — the full RP cycle per crop year: the guarantee (projected
//      price) being set or already set, and the harvest price that RP will settle against.
//      The number that matters is the SPREAD: on RP the revenue guarantee revalues at the
//      HIGHER of the two, so harvest above projected means every grower's guarantee steps up
//      and no price-driven loss; harvest below means RP pays on the price decline alone.
//   2. COVERAGE MIX — where the book actually sits: % of farms at each coverage level, and
//      SCO/ECO adoption. Shows the shape of the book and where the room is.
function CoverageAdvisorDashboard({ crop, cropYear }) {
  const [d, setD] = React.useState({ loading: true });
  const spec = CEPP_NY[crop];
  const today = new Date().toISOString().slice(0, 10);
  const yrs = [cropYear, cropYear + 1];

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!spec) { setD({ loading: false }); return; }
      try {
        const contracts = yrs.map(y => cepContract(spec, y));
        // Window averages come from the CONTRACT's own daily bars, not from
        // futures_settles_daily — that table is a rolling forward-curve snapshot that
        // only retains ~50 days, so averaging a discovery window out of it silently
        // drops the early days. Measured on wheat's July 2026 window: 20 bars there
        // ($6.5349) against 21 in futures_contract_history ($6.5098). RMA said $6.52.
        const syms = yrs.map(y => discoveryContractSym(crop, y)).filter(Boolean);
        const [settles, rma, mix, adopt] = await Promise.all([
          syms.length ? sbFetch("futures_contract_history", { select: "contract_sym,bar_date,close",
            filters: [`contract_sym=in.(${syms.join(",")})`],
            order: "bar_date.asc" }).catch(() => []) : Promise.resolve([]),
          // plan_code 02 pinned: rma_prices also carries plan-90 fixed elections, and for a
          // futures-discovered crop that is a different kind of number entirely.
          sbFetch("rma_prices", { select: "crop_year,projected_price,harvest_price,volatility",
            filters: [`commodity_code=eq.${spec.code}`, `type_code=eq.${spec.type}`,
                      `practice_code=eq.${spec.practice}`, `state_code=eq.${RMA_FEED_STATE}`,
                      "plan_code=eq.02", `crop_year=in.(${yrs.join(",")})`] }).catch(() => []),
          sbFetch("v_coverage_mix", { filters: [`crop_year=eq.${cropYear}`, `crop=eq.${crop}`] }).catch(() => []),
          sbFetch("v_coverage_adoption", { filters: [`crop_year=eq.${cropYear}`, `crop=eq.${crop}`] }).catch(() => []),
          sbFetch("crop_coverage", { select: "liability,policy_id,plan",
            filters: [`crop=eq.${crop}`] }).catch(() => []),
        ]);
        if (!alive) return;
        setD({ loading: false, settles: settles || [], rma: rma || [], mix: mix || [],
               adopt: (adopt || [])[0] || null });
      } catch { if (alive) setD({ loading: false }); }
    })();
    return () => { alive = false; };
  }, [crop, cropYear]);

  if (!spec) return null;
  if (d.loading) return <div style={{ padding:"18px 4px", color:"#677167", fontSize:12.5 }}><UiSpinner />Loading coverage dashboard…</div>;

  const $ = n => n == null ? "—" : "$" + (Math.round(n * 100) / 100).toFixed(2);
  const officialFor = y => (d.rma || []).find(r => +r.crop_year === y) || null;
  // Contract bars for the crop year's discovery contract, shaped for cepWindow.
  const barsFor = y => {
    const sym = discoveryContractSym(crop, y);
    return (d.settles || []).filter(r => r.contract_sym === sym);
  };
  const settlesFor = y => barsFor(y).map(r => ({ settle_date: r.bar_date, settle_price: r.close }));

  const rows = yrs.map(y => {
    const s = settlesFor(y), off = officialFor(y);
    const proj = cepWindow(spec, spec.proj, y, today, s);
    const harv = cepWindow(spec, spec.harv, y, today, s);
    // The PRICE comes off the shared ladder, not from this card's own reading of the feed.
    // cepWindow still supplies the WINDOW STATE (open/closed, days elapsed) — that's calendar
    // work the ladder doesn't do. Resolving here rather than locally is what gets this card
    // the verified-constant fallback and the 0.4-2.5x sanity band: previously, if the ADM
    // pull failed, this card dropped to a futures average while every other screen held the
    // verified number, and the two disagreed with nothing on screen to say why.
    const bars = barsFor(y);
    const projRes = resolveRmaPrice(crop, y, "projected", bars);
    const harvRes = resolveRmaPrice(crop, y, "harvest", bars);
    const projVal = projRes.price != null ? projRes.price : proj.avg;
    // ⚠ "SET" follows the ECONOMIC state the Farmer Card shows, not the machine status. A
    // settled price RMA approved (CY2027 wheat, PM-26-056) is final to a reader; tying the chip
    // to status === "final" printed PENDING here beside FINAL on the card, and would have
    // flipped the chip the day the ADM row arrived. One rule: rmaPriceDisplay.
    // The question is asked of the ladder's OWN view (no caller bars): a caller passing bars
    // always gets "estimate", by design, so its result cannot say whether RMA approved the price.
    const projSet = rmaPriceDisplay(resolveRmaPrice(crop, y, "projected"), "projected").tone === "final";
    const harvVal = harvRes.price != null ? harvRes.price : harv.avg;
    const harvSet = rmaPriceDisplay(resolveRmaPrice(crop, y, "harvest"), "harvest").tone === "final";
    const conflict = projRes.conflict || harvRes.conflict || null;
    const latest = s.length ? +s[s.length - 1].settle_price : null;
    const spread = (projVal && harvVal) ? (harvVal / projVal - 1) : null;
    return { y, proj, harv, projVal, projSet, harvVal, harvSet, latest, spread, conflict };
  });

  // $ impact of the guarantee revaluing — only meaningful once a harvest price is in play.
  const bookLiability = d.adopt && d.adopt.liability != null ? +d.adopt.liability : 0;

  const pill = (txt, tone) => {
    const TONES = { live:["#1f5c2e","#e7f3e7","#b7d3b7"], set:["#3a5570","#eef3f8","#cddcea"],
                    wait:["#8a6d1f","#fbf7ee","#e3d3ab"], done:["#6a7a6a","#f2f6f2","#dbe6db"] };
    const C = TONES[tone] || TONES.done;
    return <span style={{ fontSize:10.5, fontWeight:800, textTransform:"uppercase", letterSpacing:".04em",
      color:C[0], background:C[1], border:`1px solid ${C[2]}`, borderRadius:5, padding:"1.5px 6px" }}>{txt}</span>;
  };
  const leg = { fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#677167", fontWeight:800 };

  // Is acreage reporting still open (or recently closed, so the feed is still catching up)?
  // Only then does a zero-acre row mean "not in yet". 45 days covers AIP processing lag.
  const ardISO = (() => {
    const a = spec.ard; if (!a) return null;
    return cepDate(cropYear + a.yearOff, a.md);
  })();
  const reportingOpen = ardISO
    ? Date.parse(today) < Date.parse(ardISO) + 45 * 86400000
    : false;

  // ⚠ THE BARS AND THE HEADLINE ABOVE THEM COUNTED DIFFERENT BOOKS, AND BOTH
  // PRINTED THE WORD "farms". `v_coverage_adoption.farms` is the farms that
  // actually planted (CY2026 wheat: 103) and is what the headline and the
  // SCO/ECO block below both use; `v_coverage_mix.farms` is every farm that
  // ELECTED the crop (129). So the panel said "103 wheat farms" and then drew
  // "76 farms" at 75% underneath it, out of a different 129.
  //
  // The 26 extra farms carry $0 liability and $0 premium — all 26, measured
  // — so they are not "where the book sits" by any reading. They are also not
  // spread evenly: 21 of the 26 sit at 75%, which OVERSTATED 75% by six points
  // and understated every level below it. 50% is CAT, and it read 9% of the
  // book when it is 12% of the farms that planted — that number is a call list.
  //
  // sql/058 appends `planted_farms`. Preferring it and falling back to `farms`
  // is what makes the page safe to deploy on either side of that migration.
  const mixElected = (d.mix || []).reduce((a, r) => a + (+r.farms || 0), 0);
  const mixPlantedOf = r => Number.isFinite(+r.planted_farms) ? (+r.planted_farms) : (+r.farms || 0);
  const mixPlantedTot = (d.mix || []).reduce((a, r) => a + mixPlantedOf(r), 0);
  // FAILS OPEN. Before a crop year's acreage lands, EVERY farm is zero-acre —
  // live today on CY2027 wheat (88 elected, 0 planted) and CY2026 barley. On
  // the planted book those bars would every one of them render 0%, which reads
  // as a broken panel rather than as a book nobody has reported yet. So the
  // elections answer instead, and the panel SAYS that is what it is showing.
  const mixOnPlanted = mixPlantedTot > 0;
  const mixN = r => mixOnPlanted ? mixPlantedOf(r) : (+r.farms || 0);
  const mixTotal = mixOnPlanted ? mixPlantedTot : mixElected;
  const mixSorted = (d.mix || []).slice().sort((a, b) => +a.coverage_level - +b.coverage_level);
  const ad = d.adopt;

  return (
    <div style={{ display:"grid", gap:14, gridTemplateColumns:"repeat(auto-fit,minmax(min(430px,100%),1fr))", marginBottom:16 }}>

      {/* ── 1. price & guarantee tracker ─────────────────────────────────── */}
      <div style={{ border:"1px solid #e0ece0", borderRadius:10, background:"#fbfdfb", padding:"13px 15px" }}>
        <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:2 }}>
          <span style={leg}>Price &amp; guarantee tracker</span>
          <span style={{ fontSize:10.5, color:"#b3c1b3" }}>CBOT {spec.contractLbl} · NY</span>
        </div>
        <div style={{ fontSize:10.5, color:"#677167", marginBottom:9, lineHeight:1.45 }}>
          RP settles on the <b>higher</b> of projected vs harvest — so a harvest price above the guarantee lifts every grower's protection.
        </div>
        {rows.map(r => {
          const projPct = r.proj.pct, harvPct = r.harv.pct;
          const liab = r.y === cropYear ? bookLiability : 0;
          const dollars = (r.spread != null && liab > 0) ? liab * r.spread : null;
          return (
            <div key={r.y} style={{ borderTop:"1px solid #eef3ee", paddingTop:9, marginTop:9 }}>
              <div style={{ fontSize:13, fontWeight:800, color:"#1a2e1a", marginBottom:6 }}>{r.y} crop</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {/* guarantee */}
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                    <span style={{ fontSize:10, color:"#60735f", fontWeight:700 }}>GUARANTEE</span>
                    {r.projSet ? pill("set","set") : r.proj.state === "open" ? pill(`tracking ${r.proj.n}d`,"live")
                      : r.proj.state === "before" ? pill(`in ${r.proj.daysUntil}d`,"wait") : pill("pending","wait")}
                  </div>
                  <div style={{ fontSize:19, fontWeight:800, color: r.projSet ? "#1f5c2e" : "#5c6f5c" }}>{$(r.projVal)}</div>
                  <div style={{ fontSize:10, color:"#677167" }}>
                    {r.proj.from.slice(5)} – {r.proj.to.slice(5)}{r.proj.state === "before" && r.latest ? ` · board ${$(r.latest)}` : ""}
                  </div>
                  {projPct != null && <div style={{ height:3, background:"#e4ede4", borderRadius:2, marginTop:4 }}><div style={{ width:`${projPct}%`, height:"100%", background:"#4a9a4a", borderRadius:2 }} /></div>}
                </div>
                {/* harvest */}
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                    <span style={{ fontSize:10, color:"#60735f", fontWeight:700 }}>HARVEST</span>
                    {r.harvSet ? pill("set","set") : r.harv.state === "open" ? pill(`tracking ${r.harv.n}d`,"live")
                      : r.harv.state === "before" ? pill(`in ${r.harv.daysUntil}d`,"wait") : pill("pending","wait")}
                  </div>
                  <div style={{ fontSize:19, fontWeight:800, color: r.harvVal ? "#1a2e1a" : "#c3ccc3" }}>{$(r.harvVal)}</div>
                  <div style={{ fontSize:10, color:"#677167" }}>
                    {r.harv.from.slice(5)} – {r.harv.to.slice(5)}{r.harv.state === "open" ? ` · ${r.harv.daysLeft}d left` : ""}
                  </div>
                  {harvPct != null && <div style={{ height:3, background:"#e4ede4", borderRadius:2, marginTop:4 }}><div style={{ width:`${harvPct}%`, height:"100%", background:"#4a9a4a", borderRadius:2 }} /></div>}
                </div>
              </div>
              {r.spread != null && (
                <div style={{ marginTop:8, padding:"6px 9px", borderRadius:7, fontSize:11.5, lineHeight:1.5,
                  background: r.spread >= 0 ? "#eef6ee" : "#fdf3ee", border:`1px solid ${r.spread >= 0 ? "#cfe3cf" : "#e6b8a2"}`,
                  color: r.spread >= 0 ? "#1f5c2e" : "#a5461b" }}>
                  <b>{r.spread >= 0 ? "▲" : "▼"} Harvest {r.spread >= 0 ? "above" : "below"} guarantee {(Math.abs(r.spread) * 100).toFixed(1)}%</b>
                  {r.spread >= 0
                    ? <> — guarantees revalue up{dollars ? <> ≈ <b>+${Math.round(dollars).toLocaleString()}</b> across the book</> : null}; no price-driven loss.</>
                    : <> — RP pays the price decline even on a full yield{dollars ? <> ≈ <b>${Math.round(Math.abs(dollars)).toLocaleString()}</b> of exposure</> : null}.</>}
                  {!r.harvSet && r.harv.state === "open" && <span style={{ opacity:.75 }}> Still moving — {r.harv.daysLeft} days left.</span>}
                </div>
              )}
              {/* The ladder refused a fed price for disagreeing with the verified constant and
                  kept the verified one. Silently holding the old number is how a bad ADM pull
                  goes unnoticed, so the disagreement gets said out loud here too. */}
              {r.conflict && (
                <div style={{ marginTop:6, padding:"5px 8px", borderRadius:6, fontSize:10.5, lineHeight:1.45,
                  background:"#fbf3df", border:"1px solid #e3d3ab", color:"#8a6d1f" }}>
                  <b>Price conflict</b> — {rmaConflictText(r.conflict)}. The figure already in use is shown; nothing is swapped in silently.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── 2. coverage mix ──────────────────────────────────────────────── */}
      <div style={{ border:"1px solid #e0ece0", borderRadius:10, background:"#fbfdfb", padding:"13px 15px" }}>
        <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:9 }}>
          <span style={leg}>{cropYear} coverage mix</span>
          {/* Headline = farms that actually PLANTED it. Once the acreage reporting date has
              passed that IS the book (wheat 2026: 101, matching the authoritative ~100 growers);
              a zero-acre row then means the grower elected the crop and didn't plant it, not
              that data is missing. While reporting is still open we say so instead. */}
          <span style={{ fontSize:10.5, color:"#b3c1b3" }}>
            {(ad ? ad.farms : mixTotal)} {crop.toLowerCase()} farm{(ad ? ad.farms : mixTotal) === 1 ? "" : "s"}
            {/* A zero-acre farm means one of two things and only THIS crop's reporting date can
                tell them apart: reporting still open → data's still landing; closed → the grower
                elected the crop and chose not to plant it (normal). Can't infer it from acreage
                on other crops — each crop has its own deadline. */}
            {ad && ad.zero_acre_farms > 0
              ? reportingOpen ? ` · acreage still coming in (${ad.zero_acre_farms} not in yet)`
                              : ` · ${ad.zero_acre_farms} elected, didn't plant`
              : ""}
          </span>
        </div>
        {mixTotal === 0 ? <div style={{ fontSize:12, color:"#677167" }}>No {crop.toLowerCase()} coverage on the book for {cropYear}.</div> : (<>
          {mixSorted.map(m => {
            const pct = mixN(m) / mixTotal * 100;
            const top = pct === Math.max(...mixSorted.map(x => mixN(x) / mixTotal * 100));
            return (
              <div key={m.coverage_level} style={{ display:"grid", gridTemplateColumns:"42px 1fr 92px", gap:9, alignItems:"center", marginBottom:4 }}>
                <span style={{ fontSize:11.5, fontWeight: top ? 800 : 600, color: top ? "#1f5c2e" : "#5c6f5c", textAlign:"right" }}>{Math.round(+m.coverage_level * 100)}%</span>
                <div style={{ height:14, background:"#eef3ee", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ width:`${pct}%`, height:"100%", background: top ? "#2d6a2d" : "#8fbc8f", borderRadius:3 }} />
                </div>
                <span style={{ fontSize:11, color:"#60735f", whiteSpace:"nowrap" }}><b style={{ color:"#2a3a2a" }}>{pct.toFixed(0)}%</b> · {mixN(m)} farm{mixN(m) === 1 ? "" : "s"}</span>
              </div>
            );
          })}
          {/* The bars are on the SAME farms the headline names, so the panel states one
              population — except when nobody has reported acreage yet, where it falls
              back to the elections and says so rather than drawing six empty bars. */}
          {!mixOnPlanted && mixElected > 0 && (
            <div style={{ marginTop:8, fontSize:10, color:"#b3c1b3", lineHeight:1.45 }}>
              Measured on all {mixElected} farm{mixElected === 1 ? "" : "s"} that <b>elected</b> {crop.toLowerCase()} for {cropYear} — no acreage is reported yet, so this is what was bought, not what was planted.
            </div>
          )}
          {ad && (
            <div style={{ marginTop:11, paddingTop:10, borderTop:"1px solid #eef3ee", display:"flex", gap:22, flexWrap:"wrap" }}>
              {/* Farm % and ACRE % both, because they diverge and the gap is the story: SCO
                  growers are the big operations, so acre penetration ≠ customer penetration. */}
              {/* The band label is the CROP YEAR's, not a constant. This panel reports
                  adoption for `cropYear`, and a 2026 SCO stopped at 86 — printing 90
                  beside a 2026 count states a product that year never sold. */}
              {(() => { const _bd = scoEcoBands(cropYear); return [
                ["SCO", ad.sco_farms, ad.sco_acres, `level→${Math.round(_bd.scoTop*100)}%`],
                ["ECO", ad.eco_farms, ad.eco_acres, `${Math.round(_bd.ecoBottom*100)}→${Math.round(_bd.ecoTop*100)}%`],
              ]; })().map(([lbl, n, ac, band]) => {
                const pctF = ad.farms ? (+n) / ad.farms * 100 : 0;
                const pctA = ad.acres ? (+ac || 0) / +ad.acres * 100 : null;
                return (
                  <div key={lbl} style={{ flex:"1 1 160px" }}>
                    <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
                      <span style={{ fontSize:11, fontWeight:800, color:"#3a4a3a" }}>{lbl}</span>
                      <span style={{ fontSize:10.5, color:"#b3c1b3" }}>{band}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                      <span style={{ fontSize:20, fontWeight:800, color: pctF >= 50 ? "#1f5c2e" : "#a5461b" }}>{pctF.toFixed(0)}%</span>
                      {pctA != null && <span style={{ fontSize:11.5, color:"#60735f" }}>of farms · <b style={{ color:"#3a4a3a" }}>{pctA.toFixed(0)}%</b> of acres</span>}
                    </div>
                    <div style={{ fontSize:10.5, color:"#677167" }}>{n} of {ad.farms}{+n < ad.farms ? ` · ${ad.farms - n} without` : ""}</div>
                  </div>
                );
              })}
              {/* Endorsement rows land WITH the acreage, not before — while reporting is open a
                  farm still awaiting its acreage shows no SCO/ECO simply because none of its detail
                  has arrived, so counting it as "declined" would understate adoption. */}
              {reportingOpen && ad.zero_acre_farms > 0 && (
                <div style={{ flexBasis:"100%", fontSize:10, color:"#b3c1b3", lineHeight:1.45 }}>
                  Measured on the {ad.farms} farm{ad.farms === 1 ? "" : "s"} with acreage in. {ad.zero_acre_farms} still reporting — endorsements arrive with the acreage, so this will move.
                </div>
              )}
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}

// ── Coverage Stack ──────────────────────────────────────────────────────────
// Visualizes revenue protection as $ layers on the 0→100%-of-expected-revenue
// axis: MPCI RP (0→level), SCO (level→ its year's top), ECO (that top→95%), and the always-
// uncovered top slice. Each layer shows $ coverage + $ premium per acre.
// ARC/PLC is deliberately NOT a stacked layer — it's a base-acre government
// payment (MYA price × payment yield), not farm-revenue protection — so when an
// `arcplc` figure is passed it renders as a SEPARATE companion, never a band.

// Lay the bands out on a MINIMUM-HEIGHT scale rather than a strictly linear one:
// ECO's band is only 5% of revenue (~10px on a linear 196px bar), far too short to
// hold its label. Each covered band is floored at `minOn` px and the surplus comes
// out of the tall MPCI band, so every layer can be labeled on the bar. The % ticks
// stay pinned to the real band boundaries, so the axis still tells the truth —
// only the spacing between ticks is compressed. Slices must be ordered bottom→top.
function _stackLayout(slices, H, minOn) {
  const span = s => s.hi - s.lo;
  const total = slices.reduce((a, s) => a + span(s), 0) || 1;
  const floorOf = s => (s.on ? minOn : 13);
  const needsFloor = s => (span(s) / total) * H < floorOf(s);
  const fixed = slices.filter(needsFloor).reduce((a, s) => a + floorOf(s), 0);
  const flex = slices.filter(s => !needsFloor(s));
  const flexSpan = flex.reduce((a, s) => a + span(s), 0) || 1;
  const avail = Math.max(0, H - fixed);
  let yb = H;                                  // walk bottom → top
  const out = [];
  slices.forEach(s => {
    const h = needsFloor(s) ? floorOf(s) : avail * (span(s) / flexSpan);
    yb -= h;
    out.push({ ...s, y: yb, h });
  });
  const tickY = { 0: H };                      // pct (0-100) → y of that boundary
  out.forEach(s => { tickY[Math.round(s.hi * 100)] = s.y; });
  return { bands: out, tickY };
}

// Which coverage levels to stand side by side under the recommendation.
//
// The set is { recommendation, one step down, one step up } plus last year's level.
// Overlap collapses on its own — if last year is already a neighbour of the rec you get
// three bars, otherwise four — which is the "never show the same coverage twice" rule
// stated as a set union rather than enforced with special cases afterwards.
//
// At an edge (rec 50% or 85%) one neighbour does not exist, so it takes two steps on the
// side that does. Without that the comparison silently collapses to two bars exactly when
// the grower is at the top of the range, which is where the level conversation matters.
//
// Last year's level is added LAST and may be far away (rec 85%, last 65%). It is returned
// in its true position, gap and all — the distance IS the point, and evenly spacing them
// would misrepresent how big a move is being proposed.
// `pinned` are levels the agent added from the 8-level grid, so the grid is the control
// for this chart rather than a second display of the same thing. Pins are added AFTER the
// automatic set and go through the same de-duplication, so pinning the level a grower
// already carries changes nothing instead of drawing it twice.
const WHEAT_BAR_MAX = 6;
function wheatBarLevels(recCl, lastCl, levels = [0.50, 0.55, 0.60, 0.65, 0.70, 0.75, 0.80, 0.85], pinned = []) {
  const at = v => (v == null ? -1 : levels.findIndex(x => Math.abs(x - +v) < 1e-9));
  const i = at(recCl);
  if (i < 0) return [];
  const out = [];
  const push = v => { if (out.length < WHEAT_BAR_MAX && at(v) >= 0 && !out.some(x => Math.abs(x - +v) < 1e-9)) out.push(+v); };
  push(recCl);
  const nb = [];
  if (i - 1 >= 0) nb.push(levels[i - 1]);
  if (i + 1 < levels.length) nb.push(levels[i + 1]);
  if (nb.length < 2) { if (i - 2 >= 0) nb.push(levels[i - 2]); if (i + 2 < levels.length) nb.push(levels[i + 2]); }
  nb.forEach(push);
  push(lastCl);
  (pinned || []).forEach(push);
  return out.sort((a, b) => a - b);
}

// ── WHICH COVERAGE STACKS A UNIT DRAWS — ONE RULE, THREE SURFACES ────────────
// Kevin, 2026-09-15: "make it so I can show 2 or 3 or 4 coverage stacks if I want to
// … maybe we dont want the recommended coverage and we want to override and select
// totally different coverage levels to print."
//
// `sel == null` is AUTO: the quoted level, its neighbours and last year's election —
// what the chart has always chosen for itself. An explicit selection is exactly the
// levels the agent ticked, so two, three or four stacks is a matter of ticking two,
// three or four. THE QUOTED LEVEL IS ALWAYS DRAWN and cannot be ticked away: it is the
// coverage the sheet is quoting, and a comparison that omits it compares nothing.
//
// ⚠ The old control could only ADD. `wheatBarLevels` merges pins INTO the automatic set,
// so clicking a neighbour the set had chosen did nothing at all — there was no way to
// print fewer bars than the engine picked, which is exactly what was asked for.
function wheatChartLevels(quotedCl, lastCl, levels = [0.50, 0.55, 0.60, 0.65, 0.70, 0.75, 0.80, 0.85], sel = null) {
  if (sel == null) return wheatBarLevels(quotedCl, lastCl, levels);
  const at = v => (v == null ? -1 : levels.findIndex(x => Math.abs(x - +v) < 1e-9));
  const out = [];
  const push = v => { if (out.length < WHEAT_BAR_MAX && at(v) >= 0 && !out.some(x => Math.abs(x - +v) < 1e-9)) out.push(+v); };
  push(quotedCl);
  (sel || []).forEach(push);
  return out.sort((a, b) => a - b);
}

// What the printed sheet carries. Every section defaults ON, so a caller that passes
// nothing — the batch printer — prints exactly the document it printed before.
// `barSel` is the per-unit stack selection above, keyed by wheatUnitKey.
//
// ⚠ IT IS PASSED, NEVER READ FROM THE SCREEN. wheatUnitKey is farm-agnostic
// (COUNTY|PRACTICE), so two growers in LANCASTER|003 share a key — the same hazard
// recOverrides already carries. The batch printer walks many farms and passes none.
const WHEAT_PRINT_SECTIONS = { lastYear: true, stacks: true, recPanel: true, comparePage: true, triggerPage: true };
const wheatPrintCfg = (cfg) => ({ ...WHEAT_PRINT_SECTIONS, barSel: null, ...(cfg || {}) });

// ── EVERYTHING AN AGENT CUSTOMISES ON A WHEAT QUOTE BELONGS TO ONE FARM ──────
// BUG-078. The quoter panel owns the selected farm, and switching farms does NOT
// remount it — so every customisation map outlived the grower it was set on. Two of
// them are keyed by `wheatUnitKey` (COUNTY|PRACTICE), which is farm-AGNOSTIC by
// design, so they did not merely outlive him, they APPLIED to the next farm sharing
// that county:
//
//   measured 2026-09-15, ATWATER FARMS -> CY FARMS, both ORLEANS|003 —
//   CY FARMS' Orleans card read "OVERRIDDEN" with ATWATER's dropped SCO and his
//   50% level, and CY FARMS' printed Orleans page drew ATWATER's 50% stack beside
//   its own 60% recommendation. The document sections followed too: his sheet
//   printed with no prior-year column, no comparison page and no trigger record.
//
// It is not an edge: LIVINGSTON|003 is shared by 19 farms in the CY2027 book,
// MONROE|003 by 15, ORLEANS|003 by 9.
//
// ⚠ THE FIX IS A READ-TIME SCOPE, NOT A RESET EFFECT. An effect that clears state
// when `farmer` changes runs AFTER the first render of the new farm, so there is a
// frame in which the new grower's card is drawn from the old grower's overrides —
// and a print fired from that frame would carry them. Keying the store by farm means
// the wrong farm's settings are unreachable in the same render that selects him.
// Keeping a map per farm (rather than clearing) also means A -> B -> A returns to
// what the agent set on A, instead of quietly losing his work.
// `aph` (the APH database edit) and `units` (the ticked databases) joined 2026-09-18 — see
// resolveUnitAph. Both are frozen so a read of the EMPTY store can never be written through.
// (One line on purpose: check-wheat-compare-page lifts each of these declarations by its line.)
const WHEAT_CUSTOM_EMPTY = Object.freeze({ rec: {}, bars: {}, sections: WHEAT_PRINT_SECTIONS, aph: Object.freeze({}), units: Object.freeze({}), unitAc: Object.freeze({}) });
const wheatCustomFor = (all, farmKey) => ((farmKey && all && all[farmKey]) || WHEAT_CUSTOM_EMPTY);
// The farm this customisation belongs to. Name AND policy ids: the farm list is deduped
// by name, and the ids make a re-keyed book read as a different farm rather than
// inheriting settings under a name that now means something else.
const wheatFarmKey = (f) => (f ? String(f.name || "") + "|" + ((f.allPolicyIds || f._allPolicyIds || []).join(",")) : "");

// `compact` renders JUST the bar with a caption above and $/ac beneath, so several can
// stand side by side as a level comparison. The full form keeps the layer table, which is
// worth showing once for the recommendation but is noise repeated four times.
// `bandYear` IS REQUIRED AND IS THE BAR'S OWN CROP YEAR, not the quote's. A row
// of bars puts last year beside this year's quote, and until 2026-09-02 every
// one of them was drawn on the 2027 lattice: a CY2026 grower's ECO — a NINE-point
// 86→95 band — was painted into a FIVE-point slot labelled 90→95, with his SCO
// stretched from six points to ten beside it. The dollars came straight from the
// AIP and were right; the bands they sat in were a different product's, so the
// column could not be reconciled against his own statement. See src/scoEcoBands.js.
function CoverageStackViz({ bandYear, cl, aph, price, mpci, sco, eco, arcplc = null, compact = false, caption = null, tag = null, ratio = null, paid = null, paidNote = null, zoomFrom = 0, guideAt = null, yearLabel = null, premScale = 0, vsRec = null, guarBu = null }) {
  if (!mpci || !(aph > 0) || !(price > 0)) return null;
  const f2 = n => n == null || isNaN(n) ? "—" : "$" + (Math.round(n * 100) / 100).toFixed(2);
  const expRev = aph * price;                 // 100% reference, $/ac
  // NO axis zoom. Cutting the axis in did separate the bars, but it shrank MPCI to a
  // sliver while SCO filled the bar — telling the grower his county endorsement is the
  // bulk of his protection when MPCI is. A chart that misstates the hierarchy to make a
  // 5-point move look bigger is worse than one that is hard to read.
  //
  // The levels are separated instead by a shared dashed guide at the RECOMMENDED level,
  // drawn at the same height in every bar (they all run 0-100%), so each bar visibly sits
  // above or below it while every layer keeps its true size.
  const zf = 0;
  // Taller in compact mode: the band boundary moves 5 points of revenue between levels,
  // which is 10px on a 200px bar and 17px on 340. Physics caps how much that can help —
  // see the premium bar below for the difference that actually matters.
  const H = compact ? 300 : 200;
  const BAND = scoEcoBands(bandYear);
  const ON = { MPCI:true, SCO:!!sco, ECO:!!eco, open:false };
  const FIG = { MPCI:mpci, SCO:sco, ECO:eco, open:null };
  const COL = { MPCI:"#2d6a2d", SCO:"#6aa86a", ECO:"#a6cf9f", open:"#e6ece6" };
  const slices = scoEcoSlices(bandYear, cl).map(sl => ({
    ...sl, lo: sl.key === "MPCI" ? zf : sl.lo,
    on: ON[sl.key], color: COL[sl.key],
    prem: FIG[sl.key] ? FIG[sl.key].prem : null, cov: FIG[sl.key] ? FIG[sl.key].cov : null,
  })).filter(s => s.hi > s.lo + 1e-9);
  // Compact bars are TRUE TO SCALE. The full form inflates thin bands so each can carry
  // its label, which is fine for one bar but destroys a side-by-side: at 80% and 85% the
  // inflated layout is identical (MPCI 139 / SCO 24 / ECO 24 px), so the chart cannot show
  // the very thing it is being asked to compare. Un-floored, MPCI climbs 150 -> 160 -> 170
  // and SCO thins 30 -> 20 -> 10, which IS the trade being made.
  // minOn = 0 in compact: TRUE TO SCALE, which is what the note above promises and what the
  // side-by-side needs. Flooring thin bands so each can carry a label is right for the one
  // annotated stack (the full form, below) and wrong here — at 85% the SCO band is 5 points
  // of revenue, under any floor, so it got inflated and MPCI was squeezed to pay for it:
  // MPCI came out 230px at 80% and 231px at 85%, a one-pixel move for the step being
  // decided. Un-floored it climbs honestly and SCO visibly thins. Bands too thin to hold
  // their figures put them in the gutter beside the bar rather than being dropped.
  const { bands, tickY } = _stackLayout(slices, H, compact ? 0 : 24);
  // Compact bars are un-floored, so the axis is LINEAR and any percentage has a y — which
  // the tickY lookup does not. tickY only holds THIS bar’s own slice boundaries, so asking
  // it for the recommended level on a bar at a different level returned undefined and the
  // guide line and delta slab silently did not draw. They only ever appeared on the bar
  // that happened to sit at the recommendation, which is the one place they say nothing.
  const yAt = pct => (compact ? H * (1 - pct) : tickY[Math.round(pct * 100)]);
  const y = pct => tickY[Math.round(pct * 100)];
  const covered = slices.filter(s => s.on);
  // ⚠ A TOTAL OF WITHHELD PREMIUMS IS NOT ZERO. `|| 0` summed layers that each
  // correctly render "—" (the quoter withholds a premium where no acreage was
  // reported) into a confident "$0.00/ac paid" — on the bar directly beside the
  // dashes. Found on CRAIG A METZLER 2026-09-05: MPCI "—", ECO "— · $41",
  // total "$0.00/ac paid". Same class as the Full Strategy Report's "$0.00/ac",
  // on the screen rather than the document.
  //
  // A PARTIAL SUM IS UNKNOWN TOO, not "what we could price". A total that
  // silently drops a layer reads LOW, which is the direction that understates
  // what a grower is being asked to pay.
  const premKnown = covered.length > 0 && covered.every(s => s.prem != null);
  const totPrem = premKnown ? covered.reduce((a,s) => a + s.prem, 0) : null;
  const totCov  = covered.reduce((a,s) => a + (s.cov||0), 0);
  const topCov = eco ? Math.round(BAND.ecoTop*100) : sco ? Math.round(BAND.scoTop*100) : Math.round(cl*100);
  // Ticks mark THIS year's real boundaries — a 2026 bar rules its axis at 86, not 90.
  const ticks = [Math.round(zf*100), Math.round(cl*100), Math.round(BAND.scoTop*100), Math.round(BAND.ecoTop*100), 100]
    .filter((v,i,a) => a.indexOf(v)===i).sort((a,b) => a-b);
  // Compact bars carry a PREMIUM COLUMN beside the coverage stack. The stack answers
  // "where does my protection come from"; on its own it cannot answer "what does this
  // cost", because total coverage is near-identical across levels while premium runs 2x+.
  // Two columns, one card: structure on the left, price on the right, same axis height.
  const LX = compact ? 38 : 34, BW = compact ? 92 : 56, PW = 0, PGAP = 0, RX = compact ? 10 : 6;
  const svgW = LX + BW + PGAP + PW + RX;   // left gutter for % ticks + bar (+ premium column)
  const hdr = { fontSize:9.5, textTransform:"uppercase", letterSpacing:".04em", color:"#677167", fontWeight:800, textAlign:"right" };
  const legRow = (sw, name, band, cov, prem, bold) => (
    <div style={{ display:"grid", gridTemplateColumns:"13px 1fr 76px 76px", gap:8, alignItems:"baseline", padding:"5px 0", borderTop: bold ? "1.5px solid #cfe0cf" : "1px solid #f1f6f1" }}>
      <span style={{ width:12, height:12, borderRadius:3, background:sw||"transparent", alignSelf:"center" }} />
      <span style={{ fontSize:12.5, fontWeight: bold?800:700, color: bold?"#1f5c2e":"#2a3a2a", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{name}{band && <span style={{ fontWeight:500, color:"#a3b3a3", fontSize:10.5 }}> · {band}</span>}</span>
      <span style={{ fontSize:12.5, fontWeight:800, color: bold?"#1f5c2e":"#2a3a2a", textAlign:"right" }}>{f2(prem)}</span>
      <span style={{ fontSize:12.5, fontWeight:600, color:"#60735f", textAlign:"right" }}>{f2(cov)}</span>
    </div>
  );
  return (
    <div style={compact
      ? { display:"flex", flexDirection:"column", alignItems:"stretch", gap:2, border:"1px solid " + (tag === "rec" ? "#2d6a2d" : "#e0ece0"), borderRadius:12, background: tag === "rec" ? "#f4faf4" : "#fbfdfb", padding:"14px 14px 12px", flex:"1 1 0", minWidth:0 }
      : { display:"flex", gap:20, flexWrap:"wrap", alignItems:"center", border:"1px solid #e0ece0", borderRadius:10, background:"#fbfdfb", padding:"14px 16px", maxWidth:620 }}>
      {/* minHeight below, NOT height: the header box was a fixed 32 for the caption
          plus its tag, and the bushel guarantee is a third row that a fixed height
          would clip on every bar. A JSX comment is a CHILD here, a sibling of the
          guard below it — put one INSIDE the guard parens and it is a second
          expression where only one is allowed, and a bare block comment in JSX
          children is TEXT, not a comment. Both were tried; this is the form that
          parses. */}
      {compact && (
        <div style={{ textAlign:"center", marginBottom:2, minHeight:32 }}>
          <div style={{ fontSize:13, fontWeight:800, color: tag === "rec" ? "#1f5c2e" : (tag === "last" || tag === "elected") ? "#8a6100" : "#2a3a2a", lineHeight:1.3 }}>{caption}</div>
          {/* THE PRODUCTION GUARANTEE IN BUSHELS, beside the level that bought it —
              70% → 51.1 bu, 75% → 54.8, 80% → 58.4 is the comparison being asked for,
              and it is the figure a grower checks against their own schedule. It is the
              ENGINE's `guarBu` (or, for a prior year, the AIP's billed liability over the
              price through the shared `priorYearChain`), never `aph × cl` recomputed here:
              on a multi-database unit RMA rounds each database to the tenth and SUMS, so
              the blended APH times the level is a near-miss. A bar with no stated
              guarantee prints nothing rather than inventing one. */}
          {guarBu != null && isFinite(+guarBu) && +guarBu > 0 && (
            <div style={{ fontSize:11, fontWeight:800, color:"#2d6a2d", lineHeight:1.15 }}>
              {(+guarBu).toFixed(1)} bu/ac
              <span style={{ fontSize:9, fontWeight:700, color:"#677167", letterSpacing:".03em", textTransform:"uppercase" }}> guaranteed</span>
            </div>
          )}
          <div style={{ fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:".04em", height:12,
                        color: tag === "rec" ? "#1f7a3d" : (tag === "last" || tag === "elected") ? "#8a6100" : "#b9c4b9" }}>
            {yearLabel || (tag === "rec" ? "recommended" : tag === "elected" ? "current level" : "2027 quote")}
          </div>
        </div>
      )}
      <svg viewBox={`0 0 ${svgW} ${H+16}`} preserveAspectRatio="xMidYMin meet"
           style={compact ? { width:"100%", height:"auto", display:"block", overflow:"visible" }
                          : { width:svgW, height:H+14, overflow:"visible", flex:"0 0 auto" }}>
        {ticks.map(t => (
          <g key={t}>
            <line x1={LX-3} y1={y(t/100)} x2={LX+BW} y2={y(t/100)} stroke="#1a2e1a" strokeWidth={0.5} strokeOpacity={t===0||t===100?0.28:0.14} />
            <text x={LX-7} y={y(t/100)+3} textAnchor="end" fontSize={9} fontWeight={t===topCov?800:500} fill={t===topCov?"#3a6a3a":"#a3b3a3"}>{t}%</text>
          </g>
        ))}
        {/* The DELTA SLAB is what makes one bar read differently from the next. A 5-point
            move in the MPCI/SCO boundary is only ~17px, and every bar is the same total
            height, so the eye has nothing to catch. Marking the band BETWEEN this level and
            the recommendation — hatched, signed, labelled — gives each bar a shape the
            others do not have, and says what the move actually is: coverage changing hands
            between your own yield and the county, not more or less coverage. */}
        {guideAt != null && Math.abs(cl - guideAt) > 1e-9 && yAt(guideAt) != null && (() => {
          const yA = yAt(cl), yB = yAt(guideAt);
          if (yA == null || yB == null) return null;
          const top = Math.min(yA, yB), h = Math.abs(yA - yB);
          const up = cl > guideAt;                       // more MPCI than the rec
          const col = up ? "#1f5c2e" : "#b8860b";
          const pid = `hatch-${up ? "up" : "dn"}`;
          return (
            <g>
              <defs>
                <pattern id={pid} width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width={6} height={6} fill={up ? "#dff0df" : "#fbf0d8"} />
                  <line x1={0} y1={0} x2={0} y2={6} stroke={col} strokeWidth={2} strokeOpacity={0.55} />
                </pattern>
              </defs>
              <rect x={LX} y={top} width={BW} height={Math.max(h, 3)} fill={`url(#${pid})`} stroke={col} strokeWidth={1.2} />
              {h >= 11 && <text x={LX + 5} y={top + h/2 + 3.5} fontSize={9.5} fontWeight={800} fill={col}>
                {up ? "+" : "−"}{Math.round(Math.abs(cl - guideAt) * 100)}
              </text>}
            </g>
          );
        })()}
        {guideAt != null && yAt(guideAt) != null && (
          <line x1={LX-1} y1={yAt(guideAt)} x2={LX+BW+4} y2={yAt(guideAt)}
                stroke="#1f5c2e" strokeWidth={1.4} strokeDasharray="4 3" strokeOpacity={0.85} />
        )}
        {bands.map(s => {
          const mid = s.y + s.h/2;
          return <g key={s.key}>
            <rect x={LX} y={s.y} width={BW} height={s.h} rx={1.5} fill={s.on ? s.color : "#f0f4f0"} stroke="#fff" strokeWidth={1.5} />
            {!s.on && <rect x={LX} y={s.y} width={BW} height={s.h} fill="none" stroke="#c8d5c8" strokeDasharray="3 3" strokeWidth={0.9} />}
            {s.on && (() => {
              // PREMIUM AND COVERAGE INSIDE THE LAYER THEY BELONG TO. The layer name alone
              // says what the band is; the two numbers say what it costs and what it buys,
              // which is the whole question. Three lines where the band can hold them, two
              // where it can only take one number line, and for a band too thin for either
              // the figures move into the gutter beside the bar rather than being dropped —
              // ECO is 17px at every level and would otherwise never show a dollar.
              const ink = s.key === "ECO" ? "#2f5a2f" : "#fff";
              const dim = s.key === "ECO" ? "#4a7a4a" : "rgba(255,255,255,.86)";
              const cx = LX + BW / 2;
              const money = n => (n == null ? "—" : "$" + (Math.round(n * 100) / 100).toFixed(2));
              const whole = n => (n == null ? "—" : "$" + Math.round(n).toLocaleString());
              // Cover bought per premium dollar, PER LAYER. MPCI, SCO and ECO buy at very
              // different rates — SCO and ECO are the cheap protection precisely because they
              // need the whole county to go wrong — so one blended figure hid the comparison
              // people actually want to make. Shown in every band that has room for it.
              const rat = (s.prem > 0 && s.cov > 0) ? Math.round(s.cov / s.prem) + ":1" : null;
              if (s.h >= 58) return (<>
                <text x={cx} y={mid - 15} textAnchor="middle" fontSize={11} fontWeight={800} fill={ink}>{s.key}</text>
                <text x={cx} y={mid - 1} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={ink}>{money(s.prem)}</text>
                <text x={cx} y={mid + 11} textAnchor="middle" fontSize={9} fontWeight={600} fill={dim}>{whole(s.cov)} cov</text>
                {rat && <text x={cx} y={mid + 23} textAnchor="middle" fontSize={9} fontWeight={800} fill={dim}>{rat}</text>}
              </>);
              if (s.h >= 42) return (<>
                <text x={cx} y={mid - 9} textAnchor="middle" fontSize={10.5} fontWeight={800} fill={ink}>{s.key}</text>
                <text x={cx} y={mid + 4} textAnchor="middle" fontSize={9} fontWeight={700} fill={ink}>{money(s.prem)}</text>
                <text x={cx} y={mid + 16} textAnchor="middle" fontSize={8.5} fontWeight={600} fill={dim}>{whole(s.cov)}{rat ? " · " + rat : ""}</text>
              </>);
              if (s.h >= 26) return (<>
                <text x={cx} y={mid - 2} textAnchor="middle" fontSize={10} fontWeight={800} fill={ink}>{s.key}</text>
                <text x={cx} y={mid + 10} textAnchor="middle" fontSize={8.5} fontWeight={700} fill={dim}>{money(s.prem)} · {whole(s.cov)}{rat ? " · " + rat : ""}</text>
              </>);
              return (<>
                {s.h >= 11 && <text x={cx} y={mid + 3.5} textAnchor="middle" fontSize={8.5} fontWeight={800} fill={ink}>
                  {s.key} {money(s.prem)} · {whole(s.cov)}
                </text>}
              </>);
            })()}
          </g>;
        })}
        <text x={LX+BW/2} y={H+12} textAnchor="middle" fontSize={9} fontWeight={700} fill="#677167">{f2(expRev)}/ac exp</text>
      </svg>
      {compact ? (
        <div style={{ textAlign:"center", marginTop:4 }}>
          <div style={{ fontSize:14, fontWeight:800, color: tag === "last" ? "#8a6100" : "#1f5c2e" }}>{totPrem == null ? "—" : f2(totPrem)}<span style={{ fontSize:10, color:"#60735f", fontWeight:600 }}>/ac{tag === "last" ? " paid" : ""}</span></div>

          <div style={{ fontSize:10.5, color:"#60735f" }}>{f2(totCov)} cov</div>
          {ratio != null && <div style={{ fontSize:10, color:"#677167", marginTop:1 }}>{ratio.toFixed(0)}:1 MPCI cov/$</div>}
          {/* WHERE THE COVERAGE SITS, in dollars. Comparing TOTAL coverage across levels
              says nothing — SCO backfills whatever MPCI doesn't cover up to 90%, so the
              totals land within a dollar of each other and the chart looks like it is
              offering the same thing three times. It isn't: raising the level buys
              coverage off the COUNTY trigger and onto THIS FARM's own yield. That transfer
              is the decision, and the premium difference is its price (Kevin, 2026-08-06). */}
          {vsRec && tag !== "last" && (() => {
            const dFarm = (mpci.cov || 0) - (vsRec.mpciCov || 0);
            // ⚠ `null - 12` IS -12. With the total now withheld where the premium
            // could not be computed, an unguarded subtraction prints a confident
            // saving that is really the whole of the other bar.
            const dPrem = (totPrem == null || vsRec.prem == null) ? null : totPrem - vsRec.prem;
            if (Math.abs(dFarm) < 0.005 && dPrem != null && Math.abs(dPrem) < 0.005) return null;
            const up = dFarm > 0;
            return (
              <div style={{ marginTop:4, paddingTop:4, borderTop:"1px dashed #d8e4d8", lineHeight:1.3 }}>
                <div style={{ fontSize:11, fontWeight:800, color: up ? "#1f5c2e" : "#b8860b" }}>{f2(Math.abs(dFarm))}</div>
                <div style={{ fontSize:10.5, color:"#8a978a" }}>{up ? "onto your farm" : "onto the county"}</div>
                {dPrem != null && (
                  <div style={{ fontSize:11, fontWeight:700, color: dPrem > 0 ? "#a5461b" : "#1f7a3d", marginTop:2 }}>{dPrem >= 0 ? "+" : "\u2212"}{f2(Math.abs(dPrem))}/ac</div>
                )}
              </div>
            );
          })()}
          {paid != null && (
            <div style={{ marginTop:5, paddingTop:4, borderTop:"1px dashed #d8e4d8" }}>
              <div style={{ fontSize:12.5, fontWeight:800, color:"#8a6100" }}>{f2(paid)}<span style={{ fontSize:10.5, fontWeight:600, color:"#677167" }}>/ac paid</span></div>
              {paidNote && <div style={{ fontSize:10, color:"#a08a5a", lineHeight:1.3, maxWidth:110 }}>{paidNote}</div>}
            </div>
          )}
        </div>
      ) : (
      <div style={{ flex:"1 1 300px", minWidth:270 }}>
        <div style={{ display:"grid", gridTemplateColumns:"13px 1fr 76px 76px", gap:8, paddingBottom:2 }}>
          <span /><span style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".05em", color:"#677167", fontWeight:800 }}>Layer</span>
          <span style={hdr}>Premium</span><span style={hdr}>Coverage</span>
        </div>
        {covered.map(s => legRow(s.color, s.key === "MPCI" ? "MPCI RP" : s.key, s.band, s.cov, s.prem))}
        {legRow("#1f5c2e", "Total", `to ${topCov}% of revenue`, totCov, totPrem, true)}
        <div style={{ fontSize:10.5, color:"#677167", marginTop:9, lineHeight:1.5 }}>
          Bar = your <b>expected revenue</b>; the % ticks mark each layer's true revenue band (thin bands are enlarged to fit their label). The dashed <b>top {100-topCov}%</b> is your standing deductible. <b>MPCI</b> pays on your farm; <b>SCO/ECO</b> pay on the county.
        </div>
        {arcplc && (
          <div style={{ marginTop:10, display:"flex", gap:9, alignItems:"baseline", flexWrap:"wrap", border:"1px dashed #cdbfa0", background:"#fbf7ee", borderRadius:8, padding:"7px 11px" }}>
            <span style={{ fontSize:10.5, fontWeight:800, textTransform:"uppercase", letterSpacing:".04em", color:"#8a6d1f" }}>+ ARC/PLC</span>
            <span style={{ fontSize:12, color:"#5a4a2a" }}><b style={{ color:"#8a5a00" }}>{arcplc.label}</b> <span style={{ color:"#a5915f" }}>· separate — paid on your <b>base</b> acres, not stacked in the bar</span></span>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// Budget context for the quote — APH, price, cost, break-even — as a header strip on
// the screen where the coverage decision happens, NOT as a step in front of it.
// Break-even is CONTEXT here exactly as it is in the grain ladder: it colors the
// decision, it never drives it (see grainBreakeven in src/grainTargets.js).
function QuoteEconomics({ crop = "Wheat", aph, rateYield, acres, price, cl, guarBu,
                          premAc, sco, eco, corr }) {
  const dflt = GRAIN_COP_DEFAULTS[crop] || { cost: 0, byproduct: 0 };
  const [cost, setCost] = React.useState(dflt.cost);
  const [byp,  setByp]  = React.useState(dflt.byproduct || 0);
  const netCost = Math.max(0, (Number(cost) || 0) - (Number(byp) || 0));
  const be = grainBreakeven(Number(cost) || 0, aph, Number(byp) || 0);
  const edited = (Number(cost) || 0) !== dflt.cost || (Number(byp) || 0) !== (dflt.byproduct || 0);

  const cellLbl = { fontSize:10, textTransform:"uppercase", letterSpacing:".05em", color:"#677167", fontWeight:800, marginTop:2 };
  const cellVal = { fontSize:15, fontWeight:800, color:"#1f5c2e", lineHeight:1.15 };
  const inp = { width:58, fontSize:14, fontWeight:800, color:"#1f5c2e", border:"1px solid #cfe0cf",
                borderRadius:5, padding:"1px 5px", background:"#fff", fontFamily:"inherit" };

  return (
    <div style={{ marginBottom:11 }}>
      <div style={{ display:"flex", gap:22, flexWrap:"wrap", alignItems:"flex-start",
                    border:"1px solid #e0ece0", borderRadius:10, background:"#f7fbf7", padding:"10px 15px" }}>
        <div><div style={cellVal}>{aph == null ? "—" : aph.toFixed(1)}</div><div style={cellLbl}>APH bu</div></div>
        {rateYield != null && <div><div style={{ ...cellVal, color:"#3a6a3a" }}>{rateYield.toFixed(1)}</div><div style={cellLbl}>rate yield</div></div>}
        <div><div style={cellVal}>{acres ? Math.round(acres).toLocaleString() : "—"}</div><div style={cellLbl}>acres</div></div>
        <div><div style={cellVal}>{price ? "$" + (+price).toFixed(2) : "—"}</div><div style={cellLbl}>proj price</div></div>
        <div>
          <div style={{ display:"flex", alignItems:"baseline", gap:3 }}>
            <span style={{ ...cellVal, fontSize:13 }}>$</span>
            <input type="number" value={cost} onChange={e => setCost(e.target.value)} style={inp} />
          </div>
          <div style={cellLbl}>cost /ac{edited ? " · edited" : ""}</div>
        </div>
        {(dflt.byproduct > 0 || byp > 0) && (
          <div>
            <div style={{ display:"flex", alignItems:"baseline", gap:3 }}>
              <span style={{ ...cellVal, fontSize:13 }}>$</span>
              <input type="number" value={byp} onChange={e => setByp(e.target.value)} style={inp} />
            </div>
            <div style={cellLbl}>straw credit</div>
          </div>
        )}
        <div style={{ borderLeft:"1px solid #dbe8db", paddingLeft:20 }}>
          <div style={{ ...cellVal, color:"#8a5a00" }}>{be == null ? "—" : "$" + be.toFixed(2)}</div>
          <div style={cellLbl}>break-even /bu</div>
        </div>
      </div>
      <div style={{ fontSize:10, color:"#677167", marginTop:5 }}>
        Cost starts from the book default for {crop.toLowerCase()} and is yours to change — the farmer in front of you has his own number.
        Break-even = (cost − by-product credit) ÷ APH. It's <b>context for</b> the coverage decision, not a trigger for it.
      </div>
    </div>
  );
}

// ── CV AND BASIS, IN WORDS, FOR A PRINTED SHEET ─────────────────────────────
//
// Kevin, 2026-09-30: "Can we add CV and Basis to the print quote and full
// strategy report?" One formatter, so the quote page and the Full Strategy
// Report cannot state the same farm's volatility two different ways — the rule
// this file keeps recording (three news handlers, two guarantee engines).
//
// ⚠ EACH FIGURE IS OMITTED WHEN IT IS NOT KNOWN, AND THE CV SAYS WHY. `cvOf`
// returns null under THREE years of history, which is a different fact from
// "this farm is steady": printing 0% or an em-dash there invites a reader to
// treat a farm we cannot measure as one with no volatility, and that is the
// direction that talks somebody out of own-farm coverage. It reads "<3yr", the
// same words the quoter's county table uses.
//
// ⚠ AND BASIS IS NEVER PRINTED WITHOUT THE COUNTY YIELD IT IS BUILT FROM. A
// bare "0.75x" is uncheckable; "0.75x of 76 bu county expected" can be read
// against the grower's own APH on the same line.
function wheatYieldContextHtml(cvPct, basisX, ctyExpBu) {
  const bits = [];
  bits.push("CV " + (cvPct != null ? cvPct.toFixed(0) + "%" : "&lt;3yr"));
  if (basisX != null && ctyExpBu != null) {
    bits.push("basis " + basisX.toFixed(2) + "&times; of " + Math.round(ctyExpBu) + " bu county expected");
  } else if (ctyExpBu != null) {
    bits.push("county expected " + Math.round(ctyExpBu) + " bu");
  }
  return bits.length ? " &middot; " + bits.join(" &middot; ") : "";
}

// ── Full Strategy Report helpers (module-level, self-contained) ─────────────
// Assemble a 3-page package from the quoter: ARC/PLC election rec · coverage quote
// · marketing plan. These build branded HTML pages that reuse WHEAT_PRINT_CSS.
const _rptCap = s => String(s || '').toLowerCase().replace(/\b\w/g, m => m.toUpperCase());
const _rptEsc = s => String(s == null ? '' : s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
const _rptD2 = n => n == null || isNaN(n) ? '—' : '$' + (Math.round(n * 100) / 100).toFixed(2);
const _rptMON = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Print/HTML twin of <CoverageStackViz> — same bands + $ per layer, emitted as an
// inline-SVG string for the print document (which is document.write'd, not React).
// Uses inline styles only (the print CSS styles bare table/th/td globally).
const _rptStackHtml = ({ bandYear, cl, aph, price, mpci, sco, eco, arcplc }) => {
  if (!mpci || !(aph > 0) || !(price > 0)) return '';
  const expRev = aph * price;
  const H = 160, LX = 30, BW = 46, RX = 6, W = LX + BW + RX;
  const BAND = scoEcoBands(bandYear);
  const ON = { MPCI:true, SCO:!!sco, ECO:!!eco, open:false };
  const FIG = { MPCI:mpci, SCO:sco, ECO:eco, open:null };
  const COL = { MPCI:'#2d6a2d', SCO:'#6aa86a', ECO:'#a6cf9f', open:'#f0f4f0' };
  const slices = scoEcoSlices(bandYear, cl).map(sl => ({
    k: sl.key, lo: sl.lo, hi: sl.hi, band: sl.band.replace(/→/g, '&rarr;'),
    on: ON[sl.key], c: COL[sl.key],
    prem: FIG[sl.key] ? FIG[sl.key].prem : null, cov: FIG[sl.key] ? FIG[sl.key].cov : null,
  })).filter(s => s.hi > s.lo + 1e-9);
  const covd = slices.filter(s => s.on);
  const totP = covd.reduce((a, s) => a + (s.prem || 0), 0);
  const totC = covd.reduce((a, s) => a + (s.cov || 0), 0);
  const topCov = eco ? Math.round(BAND.ecoTop * 100) : sco ? Math.round(BAND.scoTop * 100) : Math.round(cl * 100);
  const ticks = [0, Math.round(cl * 100), Math.round(BAND.scoTop * 100), Math.round(BAND.ecoTop * 100), 100]
    .filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b);
  // Minimum-height scale (see _stackLayout) so ECO's 5% band can carry its own label.
  const { bands, tickY } = _stackLayout(slices.map(s => ({ ...s, key: s.k })), H, 20);
  const y = p => tickY[Math.round(p * 100)];
  const bars = bands.map(s => {
    const mid = s.y + s.h / 2;
    return `<rect x="${LX}" y="${s.y}" width="${BW}" height="${s.h}" fill="${s.on ? s.c : '#f0f4f0'}" stroke="#fff" stroke-width="1.2"/>`
      + (!s.on ? `<rect x="${LX}" y="${s.y}" width="${BW}" height="${s.h}" fill="none" stroke="#c8d5c8" stroke-dasharray="3 3" stroke-width="0.8"/>` : '')
      + (s.on ? `<text x="${LX + BW / 2}" y="${mid + 3}" text-anchor="middle" font-size="8.5" font-weight="700" fill="${s.k === 'ECO' ? '#2f5a2f' : '#fff'}">${s.k}</text>` : '');
  }).join('');
  const tks = ticks.map(t => `<line x1="${LX - 3}" y1="${y(t / 100)}" x2="${LX + BW}" y2="${y(t / 100)}" stroke="#1a2e1a" stroke-width="0.4" stroke-opacity="${t === 0 || t === 100 ? 0.28 : 0.14}"/>`
    + `<text x="${LX - 6}" y="${y(t / 100) + 3}" text-anchor="end" font-size="7" font-weight="${t === topCov ? 700 : 400}" fill="${t === topCov ? '#3a6a3a' : '#a3b3a3'}">${t}%</text>`).join('');
  const row = (sw, name, band, prem, cov, bold) => `<div style="display:flex;align-items:baseline;gap:7px;padding:3px 0;border-top:${bold ? '1.2px solid #cfe0cf' : '1px solid #f1f6f1'}">`
    + `<span style="width:9px;height:9px;border-radius:2px;background:${sw};display:inline-block"></span>`
    + `<span style="flex:1;font-size:9.5pt;font-weight:${bold ? 800 : 700};color:${bold ? '#1f5c2e' : '#2a3a2a'}">${name} <span style="font-weight:500;color:#a3b3a3;font-size:7.5pt">&middot; ${band}</span></span>`
    + `<span style="width:64px;text-align:right;font-size:9.5pt;font-weight:800;color:${bold ? '#1f5c2e' : '#2a3a2a'}">${_rptD2(prem)}</span>`
    + `<span style="width:64px;text-align:right;font-size:9.5pt;font-weight:600;color:#60735f">${_rptD2(cov)}</span></div>`;
  const hdr = `<div style="display:flex;align-items:baseline;gap:7px;padding-bottom:2px"><span style="width:9px"></span>`
    + `<span style="flex:1;font-size:7pt;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#677167">Layer</span>`
    + `<span style="width:64px;text-align:right;font-size:7pt;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:#677167">Premium</span>`
    + `<span style="width:64px;text-align:right;font-size:7pt;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:#677167">Coverage</span></div>`;
  const arc = arcplc ? `<div style="margin-top:8px;border:1px dashed #cdbfa0;background:#fbf7ee;border-radius:6px;padding:5px 9px;font-size:8.5pt;color:#5a4a2a">`
    // The engine's read, worded by electionLabel — never a pick made at an assumed yield.
    // No ARC/PLC dollars print (Kevin, 2026-11-01: "Do not include dollars in ARC/PLC data").
    + `<b style="color:#8a6d1f;text-transform:uppercase;letter-spacing:.04em;font-size:7.5pt">+ ARC/PLC</b> &nbsp;<b style="color:#8a5a00">${_rptEsc(arcplc.label)}</b>`
    + ` `
    + `<span style="color:#a5915f">&middot; separate program &mdash; paid on your <b>base</b> acres (MYA price &times; payment yield), not stacked in the bar</span></div>` : '';
  return `
  <div style="margin-top:14px;border:1px solid #e0ece0;border-radius:8px;background:#fbfdfb;padding:10px 12px;display:flex;gap:16px;align-items:center;page-break-inside:avoid">
    <svg width="${W}" height="${H + 13}" style="flex:0 0 auto">${tks}${bars}
      <text x="${LX + BW / 2}" y="${H + 11}" text-anchor="middle" font-size="7" font-weight="700" fill="#677167">${_rptD2(expRev)}/ac exp</text></svg>
    <div style="flex:1">
      ${hdr}
      ${covd.map(s => row(s.c, s.k === 'MPCI' ? 'MPCI RP' : s.k, s.band, s.prem, s.cov)).join('')}
      ${row('#1f5c2e', 'Total', `to ${topCov}% of revenue`, totP, totC, true)}
      <div style="font-size:8pt;color:#677167;margin-top:6px;line-height:1.45">Bar = your <b>expected revenue</b>; the % ticks mark each layer's true revenue band (thin bands are enlarged to fit their label). The dashed <b>top ${100 - topCov}%</b> is your standing deductible. <b>MPCI</b> pays on your farm; <b>SCO/ECO</b> pay on the county.</div>
      ${arc}
    </div>
  </div>`;
};
// Print/HTML twin of the on-screen coverage-level COMPARISON — the row of bars, not the
// single stack _rptStackHtml draws. The printed quote is the artifact a grower is handed
// and emailed, so it has to carry the same argument the screen makes.
//
// TRUE TO SCALE, and that is the whole point of the chart. _stackLayout can floor thin
// bands so each can carry its own label, which is right for ONE annotated stack and wrong
// for a side-by-side: at 80% the SCO band is 10 points of revenue, under the floor, so it
// got inflated and MPCI was squeezed to pay for it. MPCI then ran 119 / 127 / 129 px across
// 70 / 75 / 80% — a 2px move for the step the grower is actually being asked about. Passing
// minOn = 0 gives 160 / 171 / 183 with SCO thinning 46 / 34 / 23, which IS the trade.
// Bands too thin to hold their figures list them under the bar instead of being dropped.
//
// `bars` = [{ cl, year, tag, aph, price, m:{prem,cov}, sc, ec, prem, ratio }]
// `guideAt` = the recommended level; every 2027 bar draws the hatched slab against it,
// which is what gives each bar a shape the others don't have.
const _rptBarsHtml = ({ bars, guideAt, premScale }) => {
  if (!bars || !bars.length) return '';
  // ⚠ MEASURED, NOT GUESSED — AND THE PREVIOUS TWO GUESSES BOTH OVERFLOWED. This was
  // 250 ("overflowed the sheet"), then 196, and at 196 the quote page still rendered
  // 1,036px against a 960px letter sheet, so `break-inside: avoid` pushed the whole
  // block to the next sheet and left a BLANK one in front of it — four of them in
  // Howlett's two-county quote, which is the "several blank pages" Kevin reported on
  // 2026-10-13. Nothing here was wrong about the arithmetic; the sheet simply did not
  // fit and nothing measured it.
  // 168 is what `check-screens --print` measures as fitting with real headroom. Do not
  // re-tune this by eye: run that, which prints every page's height against the sheet.
  // 2026-10-13: 168 -> 140. The BFR statement the sheet now has to carry wraps to a
  // second line on the longer phrasings, and at 168 the page sat at exactly 960px — the
  // letter limit, i.e. no headroom, which is how this document printed blank pages
  // before. The bars are RELATIVE heights, so nothing a reader compares changes.
  const H = 140, LX = 30, BW = 86;
  const W = LX + BW + 8;
  const money = n => (n == null || isNaN(n) ? '—' : '$' + (Math.round(n * 100) / 100).toFixed(2));
  const pScale = premScale > 0 ? premScale : Math.max(0, ...bars.map(b => b.prem || 0));
  // The recommended bar, so every other bar can say what moving to it actually does.
  // TOTAL coverage barely moves across levels — SCO backfills whatever MPCI doesn't cover
  // up to 90% — so a reader comparing totals concludes nothing changes. What changes is
  // WHERE the coverage sits: raising the MPCI level buys coverage off the COUNTY trigger
  // and onto THIS FARM's own yield, and the premium difference is the price of that move.
  const recBar = bars.find(b => b.tag === 'rec' || b.tag === 'sel') || null;

  const one = (b) => {
    const cl = b.cl, expRev = (b.aph || 0) * (b.price || 0);
    if (!(expRev > 0)) return '';
    // `b.year` is the BAR's crop year and always has been — it was already read
    // two lines down for `isLast`. It just never reached the lattice, so the
    // printed sheet carried the same wrong-product bands as the screen.
    const ON = { MPCI:true, SCO:!!b.sc, ECO:!!b.ec, open:false };
    const FIG = { MPCI:b.m, SCO:b.sc, ECO:b.ec, open:null };
    const COL = { MPCI:'#2d6a2d', SCO:'#6aa86a', ECO:'#a6cf9f', open:'#e6ece6' };
    const slices = scoEcoSlices(b.year, cl).map(sl => ({
      ...sl, on: ON[sl.key], color: COL[sl.key],
      prem: FIG[sl.key] ? FIG[sl.key].prem : null, cov: FIG[sl.key] ? FIG[sl.key].cov : null,
    })).filter(s => s.hi > s.lo + 1e-9);
    const { bands } = _stackLayout(slices, H, 0);
    const yAt = p => H * (1 - p);
    const isLast = b.year === 2026;
    const d0 = n => (n == null || isNaN(n) ? '—' : '$' + Math.round(n));

    // Same three tiers the on-screen bar uses, and they fit for the same reason: the
    // viewBox is the screen's proportions. ECO is 5 points of revenue = 15 units on a
    // 300-unit bar, which holds one 8.5-unit line. The earlier print bar was 132 units on
    // a 52-wide box, so ECO came out ~6 units — nothing fits, and every figure had to move
    // to a table underneath. Geometry was the whole problem, not the labelling.
    const bandSvg = bands.map(s => {
      const ink = s.key === 'ECO' ? '#2f5a2f' : '#fff';
      const dim = s.key === 'ECO' ? '#4a7a4a' : 'rgba(255,255,255,.86)';
      const cx = LX + BW / 2, mid = s.y + s.h / 2;
      let txt = '';
      if (s.on && s.h >= 46) {
        txt = `<text x="${cx}" y="${mid - 11}" text-anchor="middle" font-size="11" font-weight="800" fill="${ink}">${s.key}</text>`
            + `<text x="${cx}" y="${mid + 3}" text-anchor="middle" font-size="12" font-weight="800" fill="${ink}">${money(s.prem)}</text>`
            + `<text x="${cx}" y="${mid + 16}" text-anchor="middle" font-size="9.5" fill="${dim}">${d0(s.cov)} cov</text>`;
      } else if (s.on && s.h >= 26) {
        txt = `<text x="${cx}" y="${mid - 3}" text-anchor="middle" font-size="10" font-weight="800" fill="${ink}">${s.key}</text>`
            + `<text x="${cx}" y="${mid + 9}" text-anchor="middle" font-size="10.5" font-weight="800" fill="${ink}">${money(s.prem)} &#183; ${d0(s.cov)}</text>`;
      } else if (s.on && s.h >= 11) {
        txt = `<text x="${cx}" y="${mid + 3.5}" text-anchor="middle" font-size="9.5" font-weight="800" fill="${ink}">${s.key} ${money(s.prem)} &#183; ${d0(s.cov)}</text>`;
      }
      return `<rect x="${LX}" y="${s.y}" width="${BW}" height="${s.h}" fill="${s.on ? s.color : '#f0f4f0'}" stroke="#fff" stroke-width="1.4"/>`
        + (!s.on ? `<rect x="${LX}" y="${s.y}" width="${BW}" height="${s.h}" fill="none" stroke="#c8d5c8" stroke-dasharray="4 4" stroke-width="1"/>` : '')
        + txt;
    }).join('');

    let slab = '';
    if (!isLast && guideAt != null && Math.abs(cl - guideAt) > 1e-9) {
      const yA = yAt(cl), yB = yAt(guideAt);
      const top = Math.min(yA, yB), h = Math.abs(yA - yB);
      const up = cl > guideAt, col = up ? '#1f5c2e' : '#b8860b';
      const pid = `hx${up ? 'u' : 'd'}${Math.round(cl * 100)}`;
      slab = `<defs><pattern id="${pid}" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">`
        + `<rect width="7" height="7" fill="${up ? '#dff0df' : '#fbf0d8'}"/>`
        + `<line x1="0" y1="0" x2="0" y2="7" stroke="${col}" stroke-width="2.4" stroke-opacity="0.55"/></pattern></defs>`
        + `<rect x="${LX}" y="${top}" width="${BW}" height="${Math.max(h, 3)}" fill="url(#${pid})" stroke="${col}" stroke-width="1.4"/>`
        + (h >= 16 ? `<text x="${LX + 6}" y="${top + h / 2 + 4}" font-size="11" font-weight="800" fill="${col}">${up ? '+' : '−'}${Math.round(Math.abs(cl - guideAt) * 100)}</text>` : '');
    }
    // No dashed guide line at the recommended level. The hatched slab already marks the
    // distance to the rec, and the line on top of it just read as clutter — Kevin, 2026-08-10.

    const lvPct = Math.round(cl * 100);
    // ⚠ THE AXIS IS THE BAR'S OWN YEAR TOO, and it was the half missed when
    // the BANDS were fixed on 2026-09-02. The slices came from scoEcoSlices(b.year)
    // and were right; these ticks stayed [0, level, 90, 95, 100], so a CY2026 bar
    // drew ECO across its true 86-95 and then ruled a '90%' line four points above
    // the real boundary - INSIDE the ECO band. Kevin's printed KAST FARMS sheet is
    // the evidence: correct dollars, correct band heights, an axis labelled 90
    // where the band starts at 86. A tick is a claim about where a layer begins;
    // drawn from a different product it contradicts the layer it is labelling.
    // _stackLayout is called with minOn 0 here, so the bar is true to scale and
    // yAt() is linear - which is exactly why a misplaced tick lands in the wrong
    // band rather than merely sitting at an odd height.
    const tickBand = scoEcoBands(b.year);
    const ticks = [0, lvPct, Math.round(tickBand.scoTop * 100), Math.round(tickBand.ecoTop * 100), 100]
      .filter((v, i, a) => a.indexOf(v) === i).sort((x, y) => x - y)
      .map(t => `<line x1="${LX - 4}" y1="${yAt(t / 100)}" x2="${LX}" y2="${yAt(t / 100)}" stroke="#1a2e1a" stroke-width="0.6" stroke-opacity="${t === lvPct ? 0.45 : 0.16}"/>`
        + `<text x="${LX - 7}" y="${yAt(t / 100) + 4}" text-anchor="end" font-size="10" font-weight="${t === lvPct ? 800 : 400}" fill="${t === lvPct ? '#2d6a2d' : '#b3c0b3'}">${t}%</text>`).join('');

    const pw = pScale > 0 ? Math.max(3, Math.round((b.prem || 0) / pScale * BW)) : 0;
    // 'sel' is the SAME bar as 'rec' — the one being quoted — labelled for who chose it.
    const tagTxt = isLast ? '2026 actual' : b.tag === 'rec' ? 'RECOMMENDED' : b.tag === 'sel' ? 'YOUR SELECTION' : b.tag === 'elected' ? 'current level' : '2027 quote';
    const tagBg = isLast ? '#f4f2ea' : b.tag === 'rec' ? '#1f5c2e' : '#eef3ee';
    const tagFg = isLast ? '#8a6d1f' : b.tag === 'rec' ? '#fff' : '#5c6f5c';
    const totCov = (b.m.cov || 0) + (b.sc ? b.sc.cov || 0 : 0) + (b.ec ? b.ec.cov || 0 : 0);

    let delta = '';
    if (!isLast && recBar && b !== recBar) {
      const dFarm = (b.m.cov || 0) - (recBar.m.cov || 0);
      // ⚠ `|| 0` TURNED "NOT PRICED" INTO "FREE". Where the premium is withheld —
      // no unit structure on file, or no acres reported — both sides are null, and
      // `(null || 0) - (null || 0)` is 0, which printed as "+$0.00/ac" beside a page
      // whose own header says the premium is not quoted. A grower reads that as the
      // upgrade costing nothing, which is the opposite of what is known. The
      // COVERAGE shift above it does not depend on the unit structure and still
      // prints; only the premium delta is withheld. Measured on MEISENZAHL FARMS
      // LLC / Ontario, where both the 70% and 80% bars read "+$0.00/ac".
      const dPrem = (b.prem == null || recBar.prem == null) ? null : b.prem - recBar.prem;
      const up = dFarm > 0, col = up ? '#1f5c2e' : '#b8860b';
      delta = `<div style="margin-top:4px;padding-top:3px;border-top:1px dashed #d8e4d8;line-height:1.25">`
        + `<div style="font-size:9pt;font-weight:800;color:${col}">${money(Math.abs(dFarm))}</div>`
        + `<div style="font-size:7pt;color:#8a978a">${up ? 'onto your farm' : 'onto the county'}</div>`
        + (dPrem == null
            ? `<div style="font-size:7pt;color:#677167">premium not quoted</div></div>`
            : `<div style="font-size:9pt;font-weight:700;color:${dPrem > 0 ? '#a5461b' : '#1f7a3d'}">${dPrem >= 0 ? '+' : '−'}${money(Math.abs(dPrem))}/ac</div></div>`);
    } else if (!isLast && recBar && b === recBar) {
      delta = `<div style="margin-top:4px;padding-top:3px;border-top:1px dashed #d8e4d8;font-size:7pt;color:#677167">reference</div>`;
    }

    // ⚠ THE PRODUCTION GUARANTEE IS THE ENGINE'S `guarBu`, AND A MISSING ONE PRINTS
    // NOTHING RATHER THAN `aph x cl`. On a multi-database unit RMA rounds each database's
    // guarantee to the tenth and SUMS them, so the blended APH times the level is a
    // different number — close, and not the one on the schedule. Where the bar carries no
    // guarBu (a prior year the AIP never billed) the line is omitted; it is not invented.
    const guarBuTxt = b.guarBu != null && isFinite(+b.guarBu) && +b.guarBu > 0
      ? `${(+b.guarBu).toFixed(1)} bu/ac` : null;
    return `<div style="flex:1 1 0;min-width:0;max-width:150px;text-align:center">
      <div style="font-size:12pt;font-weight:800;color:#1a2e1a;line-height:1.05">${lvPct}%</div>
      ${guarBuTxt ? `<div style="font-size:9pt;font-weight:800;color:#2d6a2d;line-height:1.1;margin-top:1px">${guarBuTxt}</div>
      <div style="font-size:6pt;color:#677167;letter-spacing:.03em;text-transform:uppercase;line-height:1.1">guaranteed</div>` : ''}
      <div style="display:inline-block;margin:2px 0 4px;padding:1px 7px;border-radius:4px;background:${tagBg};color:${tagFg};font-size:6.5pt;font-weight:800;letter-spacing:.04em;text-transform:uppercase">${tagTxt}</div>
      <svg width="100%" viewBox="0 0 ${W} ${H + 34}" preserveAspectRatio="xMidYMin meet" style="display:block;overflow:visible">
        ${ticks}${slab}${bandSvg}
        <rect x="${LX}" y="${H + 9}" width="${BW}" height="11" fill="#eef3ee" stroke="#dde8dd" stroke-width="0.6"/>
        <rect x="${LX}" y="${H + 9}" width="${pw}" height="11" fill="${b.tag === 'rec' ? '#1f5c2e' : isLast ? '#b99b4a' : '#7fa87f'}"/>
        <text x="${LX + BW / 2}" y="${H + 33}" text-anchor="middle" font-size="13" font-weight="800" fill="#1a2e1a">${money(b.prem)}/ac</text>
      </svg>
      <div style="font-size:7pt;color:#677167;margin-top:1px">${money(totCov)} total cov</div>
      ${delta}
    </div>`;
  };
  return `<div style="margin-top:7px;border:1px solid #e0ece0;border-radius:8px;background:#fbfdfb;padding:6px 8px;page-break-inside:avoid">
    <div style="font-size:7pt;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#677167;margin-bottom:4px">Coverage level comparison &middot; $/acre</div>
    <div style="display:flex;gap:11px;align-items:flex-start;justify-content:center">${bars.map(one).join('')}</div>
  </div>`;
};

// ARC/PLC per-county figures for the report come from countyRead (src/strategy/
// arcPlcElection.js) — the call the Coverage Advisor and the one-page election sheet
// make. `caArcPlcCountyFigures` used to price PLC here at a payment yield the caller
// hard-coded (60 bu) and was removed with it (work order E, 2026-09-18).

// ⚠ THE REPORT'S OWN ARC/PLC PAGE IS GONE (Kevin, 2026-09-19). It printed a wheat-only county
// table in its own words; the report now embeds the farm election sheet itself
// (farmElectionSheetSection), so one layout serves the print button and the report.
// The same page when the active program year's FSA figures are not published: it SAYS so, and prints
// no figure from another year in their place.
function caArcPlcPendingPageHtml(farmName, cropLbl, logoSrc, message) {
  return `<section class="page">
    <div class="brand"><img class="logo" src="${logoSrc}" alt="Clover Ag Risk Management" /><div class="bs">Risk Management &middot; Spartan Insurance Agency LLC</div></div>
    <div class="qt">ARC / PLC Election &middot; ${_rptEsc(cropLbl)}</div>
    <div class="farm">${_rptEsc(farmName)}</div>
    <div class="rec"><div class="rt">${_rptEsc(message)}</div></div>
  </section>`;
}
// One place decides whether a board may carry a target ladder at all. An
// EXPIRED or UNKNOWN board returns null, and every caller renders the state
// rather than a ladder — "no working window" is not "a short one".
function ladderHorizon(contractDate, asOf) {
  const h = resolveBoardHorizon({ contractDate, asOf: asOf || new Date().toISOString().slice(0, 10) });
  return { ...h, days: h.canCarryWorkingOrder ? h.tradingDays : null };
}

// Postal → FIPS, for the wheat rate tables (which key on FIPS while the book
// stores postal). The silent-mismatch trap this repo already recorded once.
const FIPS_OF_STATE = { NY: "36", PA: "42", OH: "39", MD: "24", IN: "18" };

// ── PAGE ONE OF THE STRATEGY REPORT — RETIRED 2026-09-16 ────────────────────
// strategyPageHtml printed the canonical strategy object as sentences, and those
// sentences were the ENGINE's: "the 80→85% band is too close to call ... reversing
// across leave-one-out, so the current election stands there" reached DU MOND AG LLC's
// Cayuga block beside a recommendation to CHANGE the election, and "Your 2027 reported
// wheat acreage" was asked once per county. The standard report is now
// src/strategy/reportV2Html.js over src/strategy/reportSynthesis.js, which translate
// the engines' structured fields and never pass their prose through.

// Marketing data for the report — fetch futures + compute the crop-year board's
// targets + the %-sold schedule (mirrors GrainTargetPlanner's board pick).
async function grainReportMarketing(crop, cropYear, storageMode = null) {
  const commodity = GRAIN_CROP_TO_COMMODITY[crop];
  const [ch, co] = await Promise.all([
    sbFetch('futures_contract_history', { filters: [`commodity=eq.${commodity}`], select: 'contract_sym,contract_date,bar_date,close,high', order: 'bar_date.asc' }).catch(() => []),
    sbFetch('futures_history_daily', { filters: [`commodity=eq.${commodity}`], select: 'bar_date,close', order: 'bar_date.asc' }).catch(() => []),
  ]);
  if (!ch || !ch.length) return null;
  const by = {}; ch.forEach(r => { (by[r.contract_sym] = by[r.contract_sym] || { sym: r.contract_sym, date: r.contract_date, rows: [] }).rows.push(r); });
  const nowd = new Date(), cy = nowd.getUTCFullYear(), cm = nowd.getUTCMonth() + 1;
  const contracts = Object.values(by).map(c => { const dt = new Date(c.date + 'T00:00:00'); return { ...c, mo: dt.getUTCMonth() + 1, yr: dt.getUTCFullYear() }; })
    .filter(c => c.yr > cy || (c.yr === cy && c.mo > cm)).sort((a, b) => a.date < b.date ? -1 : 1);
  const shown = contracts.filter(c => grainContractCropYear(crop, c.mo, c.yr) === cropYear);
  const boards = shown.length ? shown : contracts;
  const hm = GRAIN_HARVEST_MO[crop]; const cut = new Date(); cut.setDate(cut.getDate() + 90); const iso = cut.toISOString().slice(0, 10);
  const contract = boards.find(c => c.mo === hm && c.date > iso) || boards.find(c => c.date > iso) || boards[boards.length - 1];
  if (!contract) return null;
  // ⚠ A CONTRACT TOO NEW TO READ SAYS SO, instead of deleting the section.
  // Returning null here made the whole market-context page vanish, which reads as a
  // broken document. Three of the twenty-one live grain contracts are in this state
  // (SU27, CH28, WH28 — fifteen sessions each), and they are new rather than missing:
  // earlier bars for a deferred contract do not exist anywhere.
  const shortfall = grainTechShortfall(contract.rows);
  const tc = grainContractTech(contract.rows);
  if (!tc) return { contract, tc: null, shortfall, horizon: null, targets: null,
                    asOf: contract.rows.length ? contract.rows[contract.rows.length - 1].bar_date : null,
                    fillHorizon: null, cropYear };
  const _h = ladderHorizon(contract.date);
  const curve = grainFillCurve(co);
  // A board that cannot carry a working order gets NO targets, and says why.
  return { contract, tc, horizon: _h,
           targets: _h.days == null ? null : grainTechTargets(tc, null, 0, 100, curve, _h.days),
           // The settle the board figure is from, and the trading-day window each reach figure
           // was measured over — the curve's own bucket, not the requested horizon.
           asOf: contract.rows.length ? contract.rows[contract.rows.length - 1].bar_date : null,
           fillHorizon: (curve && _h.days != null) ? curve.horizonUsed(_h.days) : null,
           cropYear };
}
// Page 3 — FUTURES MARKET CONTEXT (2026-09-01). Was "Marketing plan": a table
// headed "Set order at", every row tagged "resting sell", a sell-now row when
// nowPct > 0, and a footer reading "sell INTO strength" and "Cap pre-harvest
// sales at your RP-insured bushels". Model-generated levels handed to a farmer
// on paper as instructions — a when-to-sell recommendation in print.
// Now: the same measured levels, labelled as ANALYTICAL REFERENCE levels with
// their historical reach, plus the farm's OWN established targets where any
// exist. Silas administers a farmer's target; it does not author one.
function grainMarketingPageHtml(farmName, crop, cropYear, mkt, logoSrc) {
  if (!mkt) return '';
  // The contract is known but too new to read — name it and say how far off it is.
  if (!mkt.tc || !mkt.targets) {
    if (!mkt.shortfall) return '';
    const b2 = `${mkt.contract.sym} (${_rptMON[mkt.contract.mo]} '${String(mkt.contract.yr).slice(2)})`;
    return `<section class="page">
    <div class="brand"><img class="logo" src="${logoSrc}" alt="Clover Ag Risk Management" /><div class="bs">Risk Management &middot; Spartan Insurance Agency LLC</div></div>
    <div class="qt">${cropYear} ${_rptEsc(crop)} Futures Market Context</div>
    <div class="farm">${_rptEsc(farmName)}</div>
    <div class="meta">No reference levels for ${b2}: ${_rptEsc(mkt.shortfall.why)}. A newly listed contract
      is thin until it has traded; this is a fact about the contract, not a gap in the data.</div>
  </section>`;
  }
  const c = mkt.contract, tc = mkt.tc, t = mkt.targets;
  const board = `${c.sym} (${_rptMON[c.mo]} '${String(c.yr).slice(2)})`;
  // The "at market" row was a sell-now tranche. The current price is still
  // shown, as the current price.
  const nowRow = `<tr><td class="lbl">${_rptD2(tc.last)} <span class="mut">current futures</span></td><td class="lbl2">where the board is today</td></tr>`;
  // ⚠ THE TOUCH COUNT IS PRINTED ONLY WHERE IT MEANS SOMETHING. The published standard is
  // that ONE touch is a random event, two to three is moderate and four or more is strong,
  // so printing "tested 1×" reports noise as evidence and printing "not yet tested" on
  // every rung of every contract — which is the common case for levels above a market
  // making new highs — is three lines of nothing. At two or more it is the thing that makes
  // the level checkable, which is the whole point of the rebuild.
  const rungEvidence = (r) => (r && r.touches >= 2
    ? ` &middot; this contract has turned back from it ${r.touches} times` : '');
  const rungRows = t.rungs.map(r => `<tr><td class="lbl">${_rptD2(r.lvl)}</td><td class="lbl2">${_rptEsc(r.lbl)}${r.fill != null ? ` &middot; reached after ${Math.round(r.fill * 100)}% of comparable historical starting days` : ''}${rungEvidence(r)}</td></tr>`).join('');
  // The "% sold by date" table was removed 2026-07-14 (Kevin: confusing). The dated pricing
  // pace now lives on the Marketing Plan tab as concrete tranche orders, not a bar/table here.
  return `<section class="page">
    <div class="brand"><img class="logo" src="${logoSrc}" alt="Clover Ag Risk Management" /><div class="bs">Risk Management &middot; Spartan Insurance Agency LLC</div></div>
    <div class="qt">${cropYear} ${_rptEsc(crop)} Futures Market Context</div>
    <div class="farm">${_rptEsc(farmName)}</div>
    <div class="meta">Analytical reference levels on ${board}, from that contract's own technicals. ${t.regime === 'downtrend' ? 'The board is off its highs.' : 'The board is above its trend average.'} These are historical price context &mdash; not targets, and not a recommendation to sell.</div>
    <table>
      <thead><tr><th class="lbl">Level</th><th class="lbl2">What it is</th></tr></thead>
      <tbody>${nowRow}${rungRows}</tbody>
    </table>
    <div class="disc">These are analytical reference levels drawn from ${_rptEsc(c.sym)}'s own technicals and how often this market has historically reached that far &mdash; not forecasts, not targets, and not a recommendation to sell or to sell at any particular time. Silas does not advise when or where to market grain; what these levels mean for your operation is your decision. Educational, not a solicitation to trade. Clover Ag / Spartan Insurance Agency &middot; (585) 233-1066 &middot; office@cloverag.com.</div>
  </section>`;
}

// ── Historical trigger record — the printed twin of ScoEcoBacktest ────────────
// The screen's Trigger history tab is the most persuasive part of an SCO/ECO
// conversation and it never left the screen: the grower walked out with the price
// and none of the record behind it. This is its own page, after the quote.
//
// It calls `backtestScoEco` — the SAME function the screen calls — rather than
// re-deriving the bands here. A second copy of that arithmetic is exactly how the
// SCO county bug of 2026-07-31 happened (one rule, three hand-copies, one kept
// current). Band selection comes from the screen, so the page prints what the
// agent is looking at.
//
// blocks: [{ label, aph, baseCl, rows }] — rows are sco_eco_county records
// (crop_year, revenue_ratio, projected_price) for that unit's county.
// `perCounty` returns { COUNTY: html } — one full page each — instead of one
// sheet carrying every county. Kevin, 2026-09-01: "should be the one page quote
// per county then one page of SCO/ECO trigger history ... per county", and a
// grower reading his Monroe quote should not have to hunt three sheets later for
// the Monroe evidence behind it.
// THE TRIGGER RECORD'S NUMBERS, ONCE. The detailed trigger page and the standard
// report's history chart both summarise the same county record at the same level, so
// the arithmetic lives here and both call it — a second copy is how the SCO county bug
// of 2026-07-31 happened. Returns null when no settled year can be scored.
// ── THE UNIT STRUCTURE A WHEAT QUOTE IS PRICED ON, AND THE YEAR IT IS FROM ──────
// The enterprise-unit discount is the largest single term in a wheat premium, so the unit
// structure is not a display detail. It is read, in order, off the prior-year coverage row,
// this year's row, and the AIP's own acreage records (m13_acreage) for the same coverage —
// newest first, and never from a crop year after the one being quoted. Where none of them
// states one it is UNKNOWN and this returns null. It never defaults: normUnit() turning a
// blank into "EU" is how 35 BASIC-unit CY2026 wheat policies came to be quoted at
// enterprise-unit rates on the card, the printed quote and the strategy report (measured
// 2026-09-17), and how DU MOND AG LLC's Wayne County read "Enterprise units" while the AIP's
// CY2025 record for it says basic unit 0003.
// ⚠ IIF KEYS ARE THE AIP'S, so a record is matched only within the AIP the policy number
// belongs to.
function wheatAipOfPolicyNumber(pn) {
  const s = String(pn || "");
  return /^MP-/.test(s) ? "RAIN_AND_HAIL" : /^[A-Z]{2}-\d{3}-\d+-\d+$/.test(s) ? "NAU" : null;
}
function wheatAipRowsFor(rows, aipRows, pNumbers) {
  const keys = new Map();
  for (const x of rows) if (x && x.iif_key) keys.set(String(x.iif_key), wheatAipOfPolicyNumber(pNumbers ? pNumbers[String(x.policy_id)] : null));
  return (aipRows || []).filter(a => a && keys.has(String(a.iif_key)) && (!keys.get(String(a.iif_key)) || a.aip === keys.get(String(a.iif_key))));
}
const WHEAT_UNIT_CODES = ["EU", "BU", "OU", "UA"];
function wheatUnitSource(r, pRow, aipRows, pYears, pNumbers, cropYear) {
  const ok = (s) => { const x = String(s || "").toUpperCase(); return WHEAT_UNIT_CODES.includes(x) ? x : null; };
  const yearOf = (row) => { const y = row && pYears ? pYears[String(row.policy_id)] : null; return y != null ? +y : null; };
  for (const row of [pRow, r]) if (row && ok(row.unit_structure)) return { unit: ok(row.unit_structure), year: yearOf(row), from: "coverage" };
  const hit = wheatAipRowsFor([r, pRow], aipRows, pNumbers)
    .filter(a => ok(a.unit_structure) && a.crop_year != null && (cropYear == null || +a.crop_year <= +cropYear))
    .sort((a, b) => +b.crop_year - +a.crop_year)[0];
  return hit ? { unit: ok(hit.unit_structure), year: +hit.crop_year, from: "acreage-report" } : null;
}
// ── THE UNIT STRUCTURE A FORWARD QUOTE IS PRICED ON ────────────────────────────
// Kevin, 2026-09-28: "we generally always quote as EU. We dont know if they qualify
// for EU until they plant. Sometimes we know they wont qualify but generally always
// quote as EU."
//
// So the QUOTE basis is an AGENCY ELECTION, not a reading of the file, and it is
// Enterprise unless the agent states otherwise. Enterprise eligibility is not
// determined until acreage is reported, so there is nothing in the file to read: the
// 2027 unit structure rides on the P11 ACREAGE record, and m13_acreage holds 8,485
// rows for CY2025, 9,991 for CY2026 and ZERO for CY2027 (measured 2026-09-28). All
// 147 CY2027 base coverages carry a NULL unit_structure. There is no year in which
// this is knowable at quote time.
//
// ⚠ THIS IS A DIFFERENT QUESTION FROM wheatUnitSource(), AND BOTH RULES MUST SURVIVE.
// That one answers "what is this farm on today" and must NEVER default — normUnit()
// turning a blank into EU is how 35 basic-unit CY2026 wheat policies came to be quoted
// at enterprise rates (measured 2026-09-17) and how DU MOND AG LLC's Wayne County read
// "Enterprise units" against an AIP record saying basic unit 0003. Reverting to that
// default re-breaks DU MOND.
//
// Carrying the prior year forward instead is the failure this replaces: FIRE CREEK
// FARMS was BU in 2026, R&H has him on EU for 2027, and the quote priced Basic. Against
// R&H's own Coverage Analyzer of 2026-09-28 every COVERAGE figure matched to the cent
// ($343.82 / $42.98 / $21.49) while MPCI premium read $26.25 against their $11.45 — 129%
// high on the sheet a grower signs. The guarantee is unit-invariant; the premium is
// mostly unit structure. That is the acre lesson on a second input.
const WHEAT_QUOTE_UNITS = ["EU", "BU", "OU"];
const WHEAT_QUOTE_UNIT_DEFAULT = "EU";
function wheatQuoteUnit(entered) {
  const s = String(entered || "").toUpperCase();
  return WHEAT_QUOTE_UNITS.includes(s) ? s : WHEAT_QUOTE_UNIT_DEFAULT;
}
// Whether wheat was planted on this coverage in `year`, per the AIP's acreage record: true
// where acres were reported, false where the record carries non-premium code Z (no acreage
// planted), null where the file does not say.
function wheatPlantedIn(r, pRow, aipRows, pNumbers, year) {
  const rows = wheatAipRowsFor([r, pRow], aipRows, pNumbers).filter(a => +a.crop_year === +year);
  if (rows.some(a => +a.reported_acreage > 0)) return true;
  if (rows.some(a => String(a.non_premium_acreage_code || "").toUpperCase() === "Z")) return false;
  return null;
}

// ── THE FALLBACK ACREAGE, AND THE CROP YEAR IT IS FROM ───────────────────────
// When a unit has no acreage on its prior-year coverage row, both the wheat card and
// the printed quote fall back to the grower's most recent PLANTED acres in production
// history — and both then labelled that figure as last year's. It is not, necessarily:
// DU MOND AG LLC's Wayne wheat is a Z (zero planted) record for 2026, and its 350
// acres are from 2025. Printed as "your 2026 acres" under a 2027 heading, that is a
// figure from one crop year labelled as another. The row carries its own year, so the
// label does now.
//
// ⚠ IT IS NOW ALSO THE COVERAGE RECOMMENDATION'S ACREAGE BASIS (2026-09-23).
// Kevin: *"We have to quote before we will ever have acreage… The optimizer needs to
// look at historical data not this years planted acres."* The recommendation is a cost
// comparison and needs some acreage to run at; CY2027's is not reported until November,
// so it runs on this — the farm's OWN reported figure, from a year that is named.
// Measured on the live CY2027 wheat book: **192 of 214 units (90%)** have one here,
// against 116 (54%) carrying a prior-year coverage figure — and the prior-year set is a
// strict SUBSET of it (prior-only = 0 units). The 22 with nothing are new ground and
// get an honest refusal rather than an invented denominator.
// It never prices a PRINTED PREMIUM: the basis it produces is flagged `estimated`, so
// `priceable` stays false and `premiumWithheld` stays true on it.
// The rule itself now lives in src/wheat/acreageBasis.js and is imported above, so the
// card, the printed quote, the recommendation engine and the book-run tools share ONE
// definition rather than four that have to be kept in step by hand.

function wheatTriggerStats(b, bands) {
  const showSco = !!(bands && bands.sco), showEco = !!(bands && bands.eco);
  const baseClPct = Math.round((b.baseCl != null ? +b.baseCl : 0.75) * 100);
  const A = +b.aph || null;
  const years = (b.rows || []).filter(r => r.revenue_ratio != null)
    .sort((x, y) => x.crop_year - y.crop_year)
    .map(r => ({ yr: r.crop_year, ratio: +r.revenue_ratio,
                 bt: backtestScoEco({ ratioPct: +r.revenue_ratio, baseClPct, aph: A, projPrice: +r.projected_price || null }) }))
    .filter(y => y.bt);
  if (!years.length) return null;
  const N = years.length;
  const indem = y => (showSco ? (y.bt.scoIndemAc || 0) : 0) + (showEco ? (y.bt.ecoIndemAc || 0) : 0);
  const scoN = years.filter(y => y.bt.scoFactor > 0).length;
  const ecoN = years.filter(y => y.bt.ecoFactor > 0).length;
  const trig = years.filter(y => (showSco && y.bt.scoFactor > 0) || (showEco && y.bt.ecoFactor > 0));
  const avgTrig = trig.length ? trig.reduce((s, y) => s + indem(y), 0) / trig.length : null;
  const avgAll = years.reduce((s, y) => s + indem(y), 0) / N;
  const worst = years.reduce((m, y) => indem(y) > (m ? indem(m) : 0) ? y : m, null);
  return { showSco, showEco, baseClPct, A, years, N, indem, scoN, ecoN, avgTrig, avgAll, worst };
}

function wheatTriggerPageHtml(farmName, blocks, bands, logoSrc, perCounty) {
  const showSco = !!(bands && bands.sco), showEco = !!(bands && bands.eco);
  const nBand = (showSco ? 1 : 0) + (showEco ? 1 : 0);
  // Nothing ticked has no page to print. The empty state is a SCREEN affordance —
  // a sheet of bars with no payout column would read as a page that failed.
  if (!nBand || !blocks || !blocks.length) return '';
  // A payout under a dollar is a real trigger, and rounding it printed "$0" — which
  // on a page handed to a grower reads as a broken cell, not as "it barely paid".
  // Monroe 2024 is the live case: a 89.96% ratio pays $0.19/ac of SCO.
  const d0 = n => n == null ? '&mdash;' : (n > 0 && n < 0.5) ? '&lt;$1' : '$' + Math.round(n).toLocaleString();
  const trigTop = showEco ? 95 : 90;

  const one = b => {
    const st = wheatTriggerStats(b, bands);
    if (!st) return '';
    const { baseClPct, A, years, N, indem, scoN, ecoN, avgTrig, avgAll, worst } = st;
    const chip = (v, lbl, col) => `<td style="text-align:center;padding:1px 8px"><div style="font-size:13pt;font-weight:800;color:${col || '#1f5c2e'};line-height:1.1">${v}</div><div style="font-size:7pt;color:#60735f;text-transform:uppercase;letter-spacing:.03em">${lbl}</div></td>`;
    const chips = [
      showSco ? chip(`${scoN}/${N}`, 'yrs SCO paid') : '',
      showEco ? chip(`${ecoN}/${N}`, 'yrs ECO paid') : '',
      A != null ? chip(d0(avgTrig), 'avg in trigger yrs') : '',
      A != null ? chip(d0(avgAll), 'avg all yrs') : '',
      (A != null && worst && indem(worst) > 0) ? chip(d0(indem(worst)), `worst yr (${worst.yr})`, '#b8500a') : '',
    ].filter(Boolean).join('');
    // Tick marks are the SELECTED bands' edges — same rule as the screen, so the
    // 90 line can't sit still while the numbers beside it move to a 95 trigger.
    const marks = []
      .concat(showEco ? [{ at: 95, top: true }, { at: 90, top: false }] : [])
      .concat(showSco ? (showEco ? [{ at: baseClPct, top: false }] : [{ at: 90, top: true }, { at: baseClPct, top: false }]) : []);
    const markHtml = marks.map(m => `<div style="position:absolute;left:${m.at}%;top:0;bottom:0;width:${m.top ? 1.4 : 1}px;background:${m.top ? '#33553a' : '#677167'}"></div>`).join('');
    const bandBottom = showSco ? baseClPct : 90;
    const rows = [...years].reverse().map(y => {
      const r = y.ratio, fill = Math.max(0, Math.min(100, r));
      const col = r < bandBottom ? '#c0392b' : r < trigTop ? '#e08a1e' : '#3a8a4a';
      const paid = (showSco && y.bt.scoFactor > 0) || (showEco && y.bt.ecoFactor > 0);
      const money = (v, on) => on ? `<td style="text-align:right;width:62px;font-weight:${v > 0 ? 800 : 400};color:${v > 0 ? '#1f5c2e' : '#c3ccc3'};border:0;padding:1.5px 4px">${v > 0 ? d0(v) : '&mdash;'}</td>` : '';
      const factor = `<td style="text-align:right;width:120px;font-size:8pt;color:${paid ? '#1f5c2e' : '#c3ccc3'};border:0;padding:1.5px 4px">${paid
        ? [showSco ? `SCO ${Math.round(y.bt.scoFactor * 100)}%` : '', showEco ? `ECO ${Math.round(y.bt.ecoFactor * 100)}%` : ''].filter(Boolean).join(' &middot; ')
        : 'no trigger'}</td>`;
      return `<tr>
        <td style="width:34px;text-align:left;font-weight:700;font-size:9pt;color:${paid ? '#1f5c2e' : '#6a7a6a'};border:0;padding:1.5px 4px">${y.yr}</td>
        <td style="border:0;padding:1.5px 4px"><div style="position:relative;height:13px;background:#eef3ee;border-radius:3px;overflow:hidden">
          <div style="position:absolute;left:0;top:0;bottom:0;width:${fill}%;background:${col};opacity:.85"></div>${markHtml}
          <div style="position:absolute;left:3px;top:0;font-size:7.5pt;font-weight:700;color:#22331f;line-height:13px">${r.toFixed(0)}%</div>
        </div></td>
        ${A != null ? money(y.bt.scoIndemAc, showSco) + money(y.bt.ecoIndemAc, showEco) : factor}
      </tr>`;
    }).join('');
    const colHead = A != null
      ? `${showSco ? '<th style="text-align:right;border:0;padding:0 4px 2px">SCO $/ac</th>' : ''}${showEco ? '<th style="text-align:right;border:0;padding:0 4px 2px">ECO $/ac</th>' : ''}`
      : '<th style="text-align:right;border:0;padding:0 4px 2px">Payout factor</th>';
    return `<div style="border:1px solid #d5e2d5;border-radius:8px;background:#fbfdfb;padding:7px 10px;margin-top:9px;page-break-inside:avoid">
      <div style="display:flex;justify-content:space-between;align-items:baseline;border-bottom:1px solid #e6efe6;padding-bottom:3px;margin-bottom:4px">
        <div style="font-size:10.5pt;font-weight:800;color:#1a2e1a">${_rptEsc(b.label)}</div>
        <div style="font-size:8pt;color:#60735f">${A != null ? `${A.toFixed(0)} bu APH &middot; ` : ''}${baseClPct}% MPCI &middot; ${years[0].yr}&ndash;${years[N - 1].yr}</div>
      </div>
      <table style="width:auto;border-collapse:collapse;margin:0 auto 4px"><tr>${chips}</tr></table>
      <table style="width:100%;border-collapse:collapse">
        <thead><tr><th style="text-align:left;border:0;padding:0 4px 2px">Yr</th><th style="text-align:left;border:0;padding:0 4px 2px">County revenue vs expected${trigTop ? ` (trigger &lt;${trigTop}%)` : ''}</th>${colHead}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
  };

  const shell = (inner, subtitle) => `<section class="page">
    <div class="brand"><img class="logo" src="${logoSrc}" alt="Clover Ag Risk Management" /><div class="bs">Risk Management &middot; Spartan Insurance Agency LLC</div></div>
    <div class="qt">Historical Trigger Record &middot; ${_rptEsc(btBandLabel({ sco: showSco, eco: showEco }))}</div>
    <div class="farm">${_rptEsc(farmName)}${subtitle ? " &mdash; " + _rptEsc(subtitle) : ""}</div>
    <div class="meta">What today's ${_rptEsc(btBandLabel({ sco: showSco, eco: showEco }))} would have paid per acre on this farm's own APH and coverage level &mdash; against every year RMA has settled.</div>
    ${inner}
    <div class="disc">Area-revenue trigger: RMA's final county revenue &divide; its expected county revenue. Dollars are PER ACRE and hold each unit's APH constant, using that year's RMA county yield and projected price &mdash; what today's coverage would have paid, not a forecast of what it will. RMA's settled county revenue begins at 2017; there is no earlier year to test against. A past trigger is not a promise of a future indemnity. Clover Ag / Spartan Insurance Agency &middot; (585) 233-1066 &middot; office@cloverag.com.</div>
  </section>`;

  if (perCounty) {
    const out = {};
    for (const b of blocks) {
      const inner = one(b);
      if (inner) out[(b.county || "").toUpperCase()] = shell(inner, b.label || b.county);
    }
    return out;
  }

  // ⚠ THIS PAGE HAD NO BOUND, AND ON A FIVE-COUNTY FARM IT WAS 1,801px — nearly two
  // sheets. `page-break-inside: avoid` on each county block then pushes the whole block
  // onto the next sheet, so the document grows a BLANK sheet in front of it and the page
  // STILL runs off the bottom. That is the "several blank pages and doesnt look good"
  // Kevin reported, on the one page in this document nothing had ever measured.
  //
  // THE SPLIT IS DERIVED FROM A MEASUREMENT, NOT TUNED BY EYE — which is the whole
  // reason the printed quote took all autumn. Measured 2026-10-13 by rendering this very
  // page with only the ROW count varying, then only the BLOCK count varying: two
  // independent series that agree to 0.1px.
  //   blocks  1 -> 519px   2 -> 839   3 -> 1160   4 -> 1480   5 -> 1801   (320.5/block)
  //   rows    3 -> 388px   6 -> 453   9 -> 519   12 -> 584   18 -> 715   24 -> 846
  // giving page chrome 198.5px, block chrome 124px, 21.8px per year row — and
  // 124 + 9 x 21.8 = 320.4, which is the block series' own slope.
  //
  // THE PER-ROW TERM IS LOAD-BEARING, NOT DECORATION. A nine-year block is 320px and TWO
  // fit a 960px sheet; at TWELVE settled years two no longer fit, and RMA settles one
  // more every year. A hardcoded "two per page" would be correct today and wrong in 2029
  // with nothing on screen to say so.
  const TRIG_SHEET_PX = 960, TRIG_PAGE_CHROME_PX = 199, TRIG_BLOCK_CHROME_PX = 124, TRIG_ROW_PX = 21.8;
  const trigBudget = TRIG_SHEET_PX - TRIG_PAGE_CHROME_PX;
  const built = [];
  for (const b of blocks) {
    const html = one(b);
    if (!html) continue;
    const st = wheatTriggerStats(b, bands);
    // A block whose stats will not resolve still RENDERED, so it still occupies a page.
    // Charging it the current nine-year height is the conservative read: over-estimating
    // costs a sparser sheet, under-estimating costs the overflow this fix exists for.
    built.push({ html, px: TRIG_BLOCK_CHROME_PX + TRIG_ROW_PX * ((st && st.N) || 9) });
  }
  if (!built.length) return '';
  // Greedy packing. A single block taller than a whole sheet STILL gets its own page —
  // refusing it would drop a county from a document that names the farm, and a county
  // silently missing from a coverage record reads as "you have nothing there".
  const bins = [];
  for (const b of built) {
    const last = bins[bins.length - 1];
    if (last && last.px + b.px <= trigBudget) { last.px += b.px; last.html += b.html; }
    else bins.push({ px: b.px, html: b.html });
  }
  const trigShell = (inner, sub) => `<section class="page">
    <div class="brand"><img class="logo" src="${logoSrc}" alt="Clover Ag Risk Management" /><div class="bs">Risk Management &middot; Spartan Insurance Agency LLC</div></div>
    <div class="qt">Historical Trigger Record &middot; ${_rptEsc(btBandLabel({ sco: showSco, eco: showEco }))}</div>
    <div class="farm">${_rptEsc(farmName)}${sub ? " &mdash; " + _rptEsc(sub) : ""}</div>
    <div class="meta">What today's ${_rptEsc(btBandLabel({ sco: showSco, eco: showEco }))} would have paid per acre, per county, on this farm's own APH and coverage level &mdash; against every year RMA has settled.</div>
    ${inner}
    <div class="disc">Area-revenue trigger: RMA's final county revenue &divide; its expected county revenue. Dollars are PER ACRE and hold each unit's APH constant, using that year's RMA county yield and projected price &mdash; what today's coverage would have paid, not a forecast of what it will. RMA's settled county revenue begins at 2017; there is no earlier year to test against. A past trigger is not a promise of a future indemnity. Clover Ag / Spartan Insurance Agency &middot; (585) 233-1066 &middot; office@cloverag.com.</div>
  </section>`;
  // A continuation sheet SAYS it is one. Three unlabelled pages with the same heading and
  // different counties read as three separate documents that got stapled together.
  return bins.map((bin, i) => trigShell(bin.html,
    bins.length > 1 ? `counties ${i + 1} of ${bins.length}` : "")).join('');
}

// ── Farm / Policy view: pick a farmer → their full crop-insurance picture (every
// insured unit across counties/crops, APH, coverage, years of yield history) with
// per-unit APH CV (own-yield volatility) + basis to county. The foundation the
// quoter + recommendations plug into. Reads crop_coverage + production_history by
// the farmer's policy ids (self-contained; no dependency on the nested farmer aggregate).
// batchOpen/batchSel are OWNED BY THE PARENT so the Renewals list can hand this
// panel a selection to print. The print engine (wheatQuotePages / openWheatPrintDoc
// / recommendWheat) stays here — it's deeply closed over this component's price and
// quote state, and lifting it would be a large refactor for no gain. Passing the
// selection instead means there is still exactly ONE batch printer.
// Defaults are no-ops rather than required props: a future second call site that
// forgets them should lose the batch panel, not white-screen Coverage Advisor.
// ── APH PROVENANCE — ONE RULE, SO SCREEN AND PDF CANNOT DISAGREE ─────────────
// An AIP-approved APH and a Silas-reconstructed one are different claims, and a
// grower reading a quote cannot tell them apart from the number. They must never
// be rendered the same way.
//
// This is not a stylistic preference. computeApprovedYield2027 rebuilds an APH
// from `production_history`, which is NOT the AIP's APH database: it lacks years
// the AIP holds, assigned yields, and AIP-side YA substitutions. Measured across
// the 40 short-database wheat cells that carry an AIP figure (2026-08-24), the
// reconstruction sits 4.95 bu from the AIP's own number on average — and fixing
// the variable T-Yield rule the same day moved that to 5.08 bu, i.e. NOT closer.
// The published rule was still the right thing to implement, but the measurement
// settles what the output is: an estimate, permanently, until the full FCIC APH
// procedure (new-producer treatment, assigned yields, added land, cups, TA
// limitations) is implemented and validated. It is not.
//
// So the hierarchy this function encodes, in Kevin's words (2026-08-24):
//   AIP approved values                    -> use them, unlabelled
//   Silas roll/reconstruction w/ evidence  -> estimate, CLEARLY MARKED
//   insufficient data or rules             -> do not manufacture an approved APH
//
// Callers pass what they know; anything unknown is falsy and simply does not
// vote. `fromAipDb` is the one that says "this came from the AIP's own P15
// databases" (weighted or blended) and therefore outranks a roll-forward.
function aphProvenance({ quoteOnly = false, edited = false, rolled = false, fromAipDb = false, priorYear = false } = {}) {
  // Order matters and mirrors the `aph` selection ladder at the call site:
  // an edited database wins, then the AIP's own databases, then a roll-forward.
  if (edited)    return { estimated: true,  label: "Estimated APH", tag: "est", why: "recomputed from an edited yield history — not an AIP-approved yield" };
  if (fromAipDb) return { estimated: false, label: "APH",           tag: "",    why: "the AIP's own approved databases, acre-weighted" };
  if (rolled)    return { estimated: true,  label: "Estimated APH", tag: "est", why: "rolled forward by Silas on this unit's elected options — the AIP has not re-approved it" };
  if (quoteOnly) return { estimated: true,  label: "Estimated APH", tag: "est", why: "rebuilt from production history — this county carries no current wheat coverage, so there is no AIP-approved yield" };
  // ⚠ AN APPROVED YIELD FROM LAST YEAR IS NOT AN ESTIMATE — IT IS THE WRONG YEAR.
  // The card falls back to the prior crop year's coverage when the AIP has not written
  // this year's base row, which is right; what was missing is that the number then
  // printed identically to a current one. It is NOT flagged `estimated`: nobody
  // modelled it, the AIP approved it — for a year that has since rolled. So it is
  // year-STAMPED, exactly the way the acres beside it already are.
  if (priorYear) return { estimated: false, priorYear: true, label: `${MPCI_WHEAT_YEAR - 1} APH`, tag: "prior",
    why: `the AIP's approved yield for ${MPCI_WHEAT_YEAR - 1}. It has not written a ${MPCI_WHEAT_YEAR} base coverage for this unit yet — commonly because a claim is still open — so this quote is built on last year's yield` };
  return { estimated: false, label: "APH", tag: "", why: "the AIP's approved yield on this coverage" };
}

// ── WHICH `farmers` ROWS IS THIS FARM — one rule, because the panel's farm is NOT a farmer row ──
// This panel groups its farms by NAME (`{ name, state, allPolicyIds, policyNums, farmerIds }`), so
// the selected farm carries NO `id`. Reading `farmer.id` there is `undefined`, and every query
// built on it answers for nobody. That is exactly how the Full Strategy Report lost its election
// sheet on 2026-09-19: the loader threw "no farm chosen" straight into a silent catch, and the
// page simply was not there — indistinguishable from a feature that had never shipped.
// `farmerIds` is a LIST because one grower has had two rows before (the CY2027 rollover minted
// five); measured 2026-09-19 every list is 1 long, which is why taking the first would look right.
const farmIdsOf = (farm) => (farm && farm.farmerIds && farm.farmerIds.length ? farm.farmerIds
  : (farm && farm.id != null ? [farm.id] : []));

const CA_NO_BATCH = {};
function FarmPolicyPanel({ farmers = [], se = {}, isMobile = false,
                           batchOpen = false, setBatchOpen = () => {}, batchSel = CA_NO_BATCH, setBatchSel = () => {},
                           onPrf = () => {} }) {
  const [q, setQ]       = useState("");
  const [sel, setSel]   = useState(null);   // selected FARM (deduped by name — combines all its policies)
  const [crop, setCrop] = useState("WHEAT");// which crop to work (pilot = wheat)
  const [cov, setCov]   = useState([]);
  const [covUnits, setCovUnits] = useState([]);   // crop_coverage_units — the APH databases behind each summary row
  const [aipAcre, setAipAcre]   = useState([]);   // m13_acreage rows for the loaded wheat coverages — see wheatUnitSource
  const [aphRows, setAphRows]   = useState([]);   // aph_database_rows — the year records inside each database
  const [openDb, setOpenDb]     = useState({});   // "county|prac" -> which database keys are expanded
  // ── the coverage-level optimizer ───────────────────────────────────────────
  // DEFAULT OFF (Kevin's call), and the price history is fetched only when it is
  // switched on — the ordinary quote pays nothing for a panel nobody opened.
  const [optOn, setOptOn]       = useState(false);
  const [optPx, setOptPx]       = useState(null);  // rma_prices, wheat, every settled year
  const [optErr, setOptErr]     = useState(null);
  const [rollUp, setRollUp]     = useState({});   // "county|prac" -> show the acre-weighted single line instead
  // "county|prac" -> which DATABASE the whole page is quoting. Null = the whole operation,
  // acre-weighted. Clicking a unit drives the columns, the bars and the economics off THAT
  // database's own three yields, which is how NAU works and the only way a 14-bushel spread
  // between his best and worst ground is a thing you can actually look at.
  // ⚠ `unitSel` IS FARM-SCOPED — it lives in the BUG-078 store below, not here. See there.
  const [ovr, setOvr]   = useState([]);   // AIP-quoted premium overrides (penny-exact, sync-safe) for 2026 actuals
  const [ph, setPh]     = useState([]);
  const [polStates, setPolStates] = useState({});   // policy_id → state (policies.state; farmers.state is null)
  const [polNumbers, setPolNumbers] = useState({}); // policy_id → policy_number (for the print heading)
  // policy_id → crop_year. WITHOUT this the panel cannot tell 2026 coverage from 2027:
  // a farm’s allPolicyIds spans every year, so both arrive in one undifferentiated pile.
  // Atwater holds wheat in both years, which printed Orleans TWICE (APH 85 and 86) and
  // quoted Niagara off 2026’s APH 91 while labelling it 2027.
  const [polYears, setPolYears] = useState({});
  const [loading, setLoading] = useState(false);
  // 2027 wheat projected price: avg of Sep-2027 CBOT SRW settles over the Aug15–Sep14 2026 window.
  // Price + volatility are ESTIMATES until RMA's discovery window (Aug 15 – Sep 14 2026) and
  // they drive BOTH the coverage and the premium. Volatility especially: it is the single
  // biggest lever on the premium, and 0.23 vs 0.20 moves every quote ~5%.
  //   vol 0.27 = R&H's OWN 2027 estimate, read off their Coverage Analyzer for policy
  //              MP-865595 (KAST FARMS, Orleans NY) dated 09/06/2026, which states it three
  //              times: the header "Price (PVF): 7.55 (0.27)", "RP Price Volatility Factor:
  //              0.27" and "SCO-RP Price Volatility Factor: 0.27". Kevin's call, 2026-09-06.
  //
  //              ⚠ IT WAS 0.20 AND THAT WAS 2026's PUBLISHED FIGURE CARRIED FORWARD. RMA has
  //              published no 2027 wheat PVF — discovery closes Sep 14 — so `rma_prices` holds
  //              ZERO CY2027 wheat rows and nothing overwrites this. Unlike the price, the
  //              volatility has NO automatic source: RMA builds it from option-implied vol and
  //              it cannot be derived from the board. So this constant IS the quote until RMA
  //              publishes, on the card AND on the printed sheet.
  //
  //              Reconciled against that illustration at 0.27, EU 80%, APH 79 / rate yield 71,
  //              458.8 ac @ $7.55 — every dollar figure matches:
  //                 MPCI   $21.09/ac   coverage $477.16   (R&H $21.09 / $477.16)
  //                 SCO     $5.68/ac   coverage  $59.65   (R&H  $5.69 /  $59.65)
  //                 ECO     $4.19/ac   coverage  $29.82   (R&H  $4.19 /  $29.82)
  //                 SCO totals: coverage 27,365 · gross 13,042 · producer 2,608 — identical
  //              The SCO cent is a DISPLAY convention, not money: the true per-acre is
  //              5.684394 on both sides; we divide the total, R&H multiplies rate × acre
  //              coverage. At 0.20 the same policy read $18.50 / $4.64 / $3.71 — $4.12/ac
  //              light, $1,890 on his acres.
  //
  //              ⚠ THE TWO AIPs DISAGREE AND THAT IS NOT RESOLVED HERE. NAU's EASYquote was
  //              observed using 0.20 for 2027. R&H is dated today and is the carrier this
  //              policy is written with. If a NAU spot-check comes out ~13% high, this is why
  //              — hold BOTH inputs equal before concluding the engine is wrong.
  //   price    = overwritten below from the live Sep-27 CBOT settle. NAU's own estimate runs a
  //              couple of days stale, so a spot-check must hold BOTH inputs equal or the
  //              comparison is meaningless — the engine is exact, the assumptions differ.
  const [px, setPx]     = useState({ price: 6.49, vol: 0.27, status: "estimate", volStatus: "estimate" });  // overridable
  const [pxAuto, setPxAuto] = useState(null);   // computed from futures (null until fetched)
  const [pxRma, setPxRma]   = useState(null);   // RMA's OFFICIAL price + vol, once published
  // RMA's published price by PRACTICE, and the organic premium over conventional.
  // The organic election is a different number, not a different rate: for 2026 wheat RMA
  // published $11.16 for certified organic (702/713) against $5.76 conventional, and
  // $5.76 — the conventional number — for TRANSITIONAL organic (712/714). Quoting an
  // organic grower off the CBOT tracking price would halve his guarantee.
  const [pxByPrac, setPxByPrac] = useState(null);   // { "003": {price, vol}, "713": {...} } — NY
  const [pxByState, setPxByState] = useState(null); // { "36": {003:{…}}, "42": {…} } — RMA prices are per STATE
  // The PRIOR crop year's published election, same shape. RMA has announced it — for
  // CY2026 wheat it is in `rma_prices` on 1,110 rows, $5.76 conventional / $11.16 organic
  // across all four states — and until now nothing read it, so the only route to a
  // prior-year price was back-solving it out of a billed liability. A policy the AIP
  // never keyed acreage on has no liability, so 89 of 131 CY2027 renewal units could not
  // produce a price and lost their year-over-year page and their prior-year bar with it.
  const [pxPrevByState, setPxPrevByState] = useState(null);
  // The ANNOUNCED organic election, typed in. A price guarantee is a figure RMA SETS — it is
  // not ours to derive — so when RMA has not announced one there is no organic quote, only a
  // box to put their number in the moment it exists. See the note on pxFor.
  const [pxOrg, setPxOrg] = useState("");

  // ⚠ THE OPTIMIZER'S PRICE HISTORY IS FETCHED ONCE, AND ONLY ONCE IT IS ASKED FOR.
  // `rma_prices` carries every settled wheat year (2015-2026 plus the open 2027) since
  // the ADM backfill; the replay needs the projected AND harvest price per year, keyed
  // by state and practice because organic carries its own election. A farm never opens
  // this panel pays for none of it.
  useEffect(() => {
    if (!optOn || optPx) return;
    let alive = true;
    (async () => {
      try {
        const rows = await sbFetch("rma_prices", {
          select: "crop_year,state_code,county_code,type_code,practice_code,projected_price,harvest_price",
          filters: ["commodity_code=eq.0011", "plan_code=eq.02"], order: "crop_year.asc,id.asc",
        });
        if (alive) { setOptPx(rows || []); setOptErr(null); }
      } catch (e) { if (alive) setOptErr((e && e.message) || String(e)); }
    })();
    return () => { alive = false; };
  }, [optOn, optPx]);

  useEffect(() => {
    let alive = true;
    (async () => {
      // RMA's official numbers WIN the moment they exist. Until discovery closes (wheat: Sep 14)
      // both fields are blank in the ADM and this returns nothing, so we fall back to tracking
      // the board. The volatility factor especially cannot be derived — RMA builds it from
      // options implied vol, which we have no feed for — so it is fetched, never estimated,
      // once available. Populated by the `rma-price` Vercel cron (api/_rma-price.js,
      // `0 14 * * *`), which range-reads the ADM and writes rma_prices. It logs to
      // sync_runs under that name, so a dead cron shows on the Data Health panel
      // rather than leaving the quoter on last year's volatility in silence.
      try {
        const rma = await sbFetch("rma_prices", {
          // plan_code 02 = Revenue Protection. rma_prices also holds APH price
          // elections (plan 90) for crops with no futures discovery, and those
          // are a different kind of number — never let one land in the quoter.
          // TYPE AND PRACTICE MUST BE PINNED, and are — by grouping rather than by
          // filtering to one. rma_prices holds one row per state/county/type/practice, and
          // for 2026 wheat the ORGANIC practices (702, 713) carry $11.16 against $5.76
          // conventional — 1,110 rows apiece across four states, all with released_date
          // NULL, so an unpinned "order by released_date desc, take the first" was a coin
          // flip that could hand the quoter the organic price and very nearly double every
          // guarantee. 011 = winter wheat. Same trap as corn GSG vs GSS silage.
          filters: [`crop_year=eq.${MPCI_WHEAT_YEAR}`, "commodity_code=eq.0011", "plan_code=eq.02",
                    "type_code=eq.011", "practice_code=in.(002,003,702,712,713,714)", "projected_price=not.is.null"],
          select: "projected_price,volatility,released_date,state_code,practice_code", order: "released_date.desc" });
        if (alive && rma && rma.length) {
          // STATE, not just practice. The practice coin flip was fixed here; the STATE one was
          // left, and it is the same bug — this query spans every state the sync pulls and then
          // took whichever row arrived first. RMA prices really do vary by state (2026 soybeans
          // $11.09 NY/PA vs $11.19 MD), so an unpinned pick can quote a New York grower off
          // Maryland's election. 2026 wheat happened to be $5.76 in all four states, which is
          // luck, not a guarantee — and this goes live the day RMA publishes 2027 (~Sep 14).
          const byS = {};
          for (const r of rma) {
            if (!r.projected_price) continue;
            const st = String(r.state_code || "");
            const m = byS[st] || (byS[st] = {});
            if (!m[r.practice_code]) m[r.practice_code] = { price:+r.projected_price, vol: r.volatility != null ? +r.volatility : null };
          }
          // 702/712 are the irrigated organic siblings; the rate tables have no wheat rows
          // for them, so their price stands in for the non-irrigated equivalent.
          for (const st of Object.keys(byS)) {
            const m = byS[st];
            if (!m["713"] && m["702"]) m["713"] = m["702"];
            if (!m["714"] && m["712"]) m["714"] = m["712"];
          }
          const byP = byS[RMA_FEED_STATE] || byS[Object.keys(byS)[0]] || {};
          if (Object.keys(byP).length) setPxByPrac(byP);
          setPxByState(byS);
          const conv = byP["003"] || byP["002"];
          if (conv) {
            setPxRma(rma.find(r => r.practice_code === "003") || rma[0]);
            setPx(p => ({ ...p, price: conv.price, vol: conv.vol != null ? conv.vol : p.vol, status: "rma", volStatus: conv.vol != null ? "rma" : "estimate" }));
            return;   // official beats any estimate — don't let the futures tracker overwrite it
          }
        }
      } catch { /* table may not exist yet — fall through to the estimate */ }
      try {
        // The CONTRACT's own bars, not futures_settles_daily. That table is a rolling
        // forward-curve snapshot holding only ~50 days; the 2027 projected window is the 31
        // days Aug 15 – Sep 14 2026, so it fits only while nothing gaps, and any day that
        // ages out is silently dropped from the average with no sign on screen. Measured on
        // wheat's July 2026 window it held 20 bars against futures_contract_history's 21
        // (a 2.5c difference). WU27 carries 268 bars back to 2025. This price sets the
        // guarantee on every 2027 wheat quote — it does not get to be approximately right.
        const sym = discoveryContractSym("WHEAT", MPCI_WHEAT_YEAR);
        const bars = await sbFetch("futures_contract_history", {
          filters:[`commodity=eq.wheat`, `contract_sym=eq.${sym}`], select:"bar_date,close", order:"bar_date.asc" });
        const rows = (bars || []).map(b => ({ settle_date: b.bar_date, settle_price: b.close }));
        if (!alive || !rows.length) return;
        const today = new Date().toISOString().slice(0, 10);
        const r = wheatProjectedPrice2027(rows, today);
        if (alive && r.price != null) { setPxAuto(r); setPx(p => ({ ...p, price: r.price, status: r.status })); }
      } catch { /* keep manual default */ }
    })();
    return () => { alive = false; };
  }, []);

  // ── THE PRIOR YEAR'S PUBLISHED ELECTION ──────────────────────────────────────
  // A SEPARATE effect on purpose: the current-year loader RETURNS EARLY when RMA has
  // published, so anything appended to it would never run in the normal case — and the
  // two years answer independent questions, so a failure on one must not blank the other.
  //
  // ⚠ THE SAME PINNING, AND FOR THE SAME REASONS. plan 02 (not the plan-90 APH election),
  // type 011 (winter wheat), and grouped per STATE and per PRACTICE rather than "order by
  // released_date and take the first" — every released_date is NULL, so an unpinned pick is
  // a coin flip that can hand a conventional unit the $11.16 ORGANIC price and nearly
  // double its coverage. Measured 2026-09-04 on CY2026: 5.76 on 002/003/712/714 and 11.16
  // on 702/713, in all four states, 1,110 rows.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rma = await sbFetch("rma_prices", {
          filters: [`crop_year=eq.${MPCI_WHEAT_YEAR - 1}`, "commodity_code=eq.0011", "plan_code=eq.02",
                    "type_code=eq.011", "practice_code=in.(002,003,702,712,713,714)", "projected_price=not.is.null"],
          select: "projected_price,volatility,state_code,practice_code" });
        if (!alive || !rma || !rma.length) return;
        const byS = {};
        for (const row of rma) {
          if (!row.projected_price) continue;
          const st = String(row.state_code || "");
          const m = byS[st] || (byS[st] = {});
          if (!m[row.practice_code]) m[row.practice_code] = { price: +row.projected_price, vol: row.volatility != null ? +row.volatility : null };
        }
        for (const st of Object.keys(byS)) {
          const m = byS[st];
          if (!m["713"] && m["702"]) m["713"] = m["702"];
          if (!m["714"] && m["712"]) m["714"] = m["712"];
        }
        if (alive) setPxPrevByState(byS);
      } catch { /* no prior-year feed — the prior column simply stays on billed figures */ }
    })();
    return () => { alive = false; };
  }, []);

  // ── The price for ONE practice ───────────────────────────────────────────────
  //   003 / 002 conventional      → px as-is
  //   714 / 712 transitional      → px as-is; RMA prices transitional organic conventionally
  //                                 (verified in the ADM across 7 crop years x 10 crops —
  //                                 identical to the conventional number every time)
  //   713 / 702 certified organic → RMA's ANNOUNCED organic election, or a figure typed in.
  //                                 Nothing else. No quote otherwise.
  //
  // A PRICE GUARANTEE IS AN ANNOUNCED FIGURE. RMA sets it; we do not derive it. That is the
  // rule, and the reason certified organic simply refuses to quote until the number exists.
  //
  // The conventional projected price is NOT an exception to that rule even though it shows a
  // number before Sep 14. RMA's own definition of it is the mean of the daily Sep-27 CBOT
  // settles across the discovery window, so tracking that same board is reproducing RMA's
  // published METHOD on public data — the running average of a figure being discovered, which
  // converges on the announced number and is replaced by it. There is no equivalent for
  // organic: RMA sets that election from organic market data we cannot see, by a method it
  // does not publish, so any number we produce would be invented.
  //
  // This previously carried an estimate of organic = conventional x last year's ratio. It was
  // removed 2026-08-06 (Kevin: "price guarantees should be announced figures, we should not be
  // trying to calculate those, they are set"), and the ADM says he was right on the arithmetic
  // too — the ratio is not stable, so the estimate was wrong as well as improper:
  //   wheat  2.19 → 2.07 → 2.03 → 1.99 → 1.99 → 2.00 → 1.94   (2020→2026)
  //   corn   2.23 → 2.09 → 1.99 → 1.88 → 1.78 → 1.63 → 1.58   falls every single year
  //   barley 2.13 flat for six years, then 1.59 in 2026       a 26% one-year cliff
  // Carried forward one year it read HIGH in 5 of 6 years on wheat (mean 2.3%, worst 6.0%),
  // 6 of 6 on corn, and would have overstated barley by 34.5% in 2026 — and HIGH is the
  // direction that overstates a guarantee on a document a grower signs.
  // tools/mpci-quoter/organic_price_history.mjs regenerates that table from the ADM.
  // Do not reintroduce it.
  // `st` is the unit's STATE FIPS (WHEAT_STATES is keyed by FIPS, same as rma_prices.state_code),
  // so a Pennsylvania unit is priced off Pennsylvania's published election rather than whichever
  // state's row happened to come back first. Falls back to New York — the state the tracking
  // price and the verified constants describe — and says so when it has to.
  const pxFor = useCallback((prac, st) => {
    const p = prac || "003";
    const vol = +px.vol;
    // Callers hand us either form — the print path carries RMA FIPS ("36"), the unit rows
    // carry policies.state ("NY"). Normalise both, and let an UNKNOWN state stay null rather
    // than silently becoming New York the way stFips()'s default would.
    const S = st == null || st === "" ? null
            : (/^\d{2}$/.test(String(st)) ? String(st) : (STATE_FIPS[String(st).toUpperCase()] || null));
    const forState = (S && pxByState && pxByState[S]) ? pxByState[S] : null;
    const map = forState || pxByPrac;
    // Conventional/transitional: RMA's published number for this state if we have it, else the
    // board tracker (which is one national contract, so it is state-neutral until RMA publishes).
    // ⚠ `volStatus` RIDES WITH THE VOLATILITY THAT WAS ACTUALLY USED. These RMA branches
    // used to omit it, so once RMA published a price every printed quote read the
    // RMA-published volatility beside an "est." tag — the price box and the sheet
    // reporting two different states for one input. RMA posts the two on their own
    // schedules, so a published price with a null volatility still carries the estimate.
    if (!isCertifiedOrganic(p)) {
      const pub = map && (map[p] || map["003"] || map["002"]);
      if (pub && pub.price > 0)
        return { price:pub.price, vol: pub.vol != null ? pub.vol : vol, status:"rma",
                 volStatus: pub.vol != null ? "rma" : wheatVolReleased(px.volStatus, vol, S), prac:p,
                 stateUsed: forState ? S : RMA_FEED_STATE, offState: !!(S && !forState) };
      return { price:+px.price, vol, status:px.status, volStatus:wheatVolReleased(px.volStatus, vol, S), prac:p };
    }
    const pub = map && (map[p] || map["713"] || map["702"]);
    // Organic claims an official volatility only off its OWN row: the fallback `vol` is the
    // conventional figure, and nothing here establishes that RMA's conventional volatility
    // is the one it applies to the organic election.
    if (pub && pub.price > 0) return { price:pub.price, vol: pub.vol != null ? pub.vol : vol, status:"rma",
             volStatus: pub.vol != null ? "rma" : wheatVolReleased("estimate", vol, S), prac:p, organic:true,
             stateUsed: forState ? S : RMA_FEED_STATE, offState: !!(S && !forState) };
    // RMA's ADM has not carried the row, but RMA HAS announced the election: CY2027 certified-organic
    // winter wheat, PM-26-056. It comes through the ONE ladder (never re-typed here), only for a state
    // the bulletin row names, and a disputed figure is not quoted.
    const orgRec = rmaPriceApprovalFor("WHEAT_ORGANIC", MPCI_WHEAT_YEAR, "projected");
    const orgLad = orgRec ? resolveRmaPrice("WHEAT_ORGANIC", MPCI_WHEAT_YEAR, "projected") : null;
    const orgState = S || RMA_FEED_STATE;
    if (orgLad && orgLad.price > 0 && !orgLad.conflict && orgRec.states.includes(orgState))
      return { price:orgLad.price, vol: orgRec.volatility != null ? orgRec.volatility : vol, status:"rma", source:"bulletin",
               bulletin: orgRec.bulletin, volStatus: orgRec.volatility != null ? "rma" : "estimate", prac:p, organic:true,
               stateUsed: orgState, offState: false };
    if (+pxOrg > 0) return { price:+pxOrg, vol, status:"organic-manual", prac:p, organic:true,
             note:`organic election entered by hand — not read from the RMA feed` };
    return { price:null, vol, status:"organic-pending", prac:p, organic:true,
             note:`RMA has not announced a ${MPCI_WHEAT_YEAR} certified-organic price election for wheat. It publishes with the projected price at the close of discovery (about Sep 14), two weeks before sales closing. Enter the announced figure to quote before then.` };
  }, [px, pxByPrac, pxByState, pxOrg]);

  // ── THE PRIOR YEAR'S PRICE FOR ONE PRACTICE ──────────────────────────────────
  // The published election only. NO board tracker and NO manual organic figure: those
  // exist for the CURRENT year because RMA has not announced it yet, and a year that has
  // closed either has an announced number or has nothing we may print.
  //
  // ⚠ AND NO CROSS-STATE FALLBACK. `pxFor` borrows New York when a state is missing,
  // which is right for a live tracking price that is one national contract. A prior year's
  // published election is per state — 2026 wheat happens to be $5.76 in all four, which the
  // current-year loader's own comment calls luck rather than a guarantee — so an unknown
  // state returns null and the prior column stays on what the AIP billed.
  const pxPrevFor = useCallback((prac, st) => {
    if (!pxPrevByState) return null;
    const p = prac || "003";
    const S = st == null || st === "" ? null
            : (/^\d{2}$/.test(String(st)) ? String(st) : (STATE_FIPS[String(st).toUpperCase()] || null));
    const m = S ? pxPrevByState[S] : null;
    if (!m) return null;
    const pub = isCertifiedOrganic(p) ? (m[p] || m["713"] || m["702"]) : (m[p] || m["003"] || m["002"]);
    return (pub && pub.price > 0) ? { price: pub.price, vol: pub.vol != null ? pub.vol : null } : null;
  }, [pxPrevByState]);

  // Quoter mode: "farm" (pull a policy) or "blank" (generic quote). Manual inputs for blank/override.
  const [mode, setMode] = useState("farm");
  const [qi, setQi] = useState({ state: "36", county: "GENESEE", aph: String(wheatYieldRefs2027("36", "GENESEE", "003").tYield ?? ""), aphTouched: false, acres: "100", scoPct: 100, ecoPct: 100, plan: "RP", unit: "EU", cl: 0.85, sco: true, eco: true, bfr: false, mc: false, ql: false, practice: "003", prospect: "" });
  const [visLevels, setVisLevels] = useState([0.65, 0.70, 0.75, 0.80, 0.85]);  // which coverage levels the grid shows
  const toggleLevel = cl => setVisLevels(v => v.includes(cl) ? (v.length > 1 ? v.filter(x => x !== cl) : v) : [...v, cl].sort());
  const [gridUnit, setGridUnit] = useState("EU");    // grid shows one unit structure at a time
  const [gridSco, setGridSco] = useState(true), [gridEco, setGridEco] = useState(true);
  // WHICH coverage levels this unit's stack comparison draws — on screen AND on the
  // printed sheet. The grid stays the full reference AND is the control for the chart, so
  // an off-script question ("what about 65?") is answered by putting 65 next to the rest
  // rather than reading a number out of a table. Capped at WHEAT_BAR_MAX bars.
  //
  // ⚠ ABSENT MEANS AUTO, NOT EMPTY. A unit the agent has not touched draws the automatic
  // set (quoted level, its neighbours, last year); the first tick seeds the selection from
  // that same set and every tick after it adds or REMOVES. The old state could only add,
  // so there was no way to print fewer stacks than the engine chose.
  //
  // ⚠ IT IS STORED PER FARM (see wheatCustomFor / BUG-078). `barSel`, `printSec` and
  // `recCfg` are all read out of one farm-keyed store below, declared after `farmer`.
  // Which county the wheat quoter is showing, or "__history" for the trigger backtests.
  // Stacking every county down one page made a three-county farm unreadable and buried
  // the recommendation for county two and three below a screen of county one.
  const [countyTab, setCountyTab] = useState(null);
  // Which SCO/ECO bands the trigger backtests are showing. Held at the PANEL, not
  // per card, because the history tab stacks one card per county — a per-card
  // checkbox would make a three-county farm tick the same box three times to ask
  // one question, and let two cards on one screen answer on different bases.
  const [btBands, setBtBands] = useState({ sco: true, eco: true });
  // Farm-mode APH options: recompute each unit's approved yield from its production history
  // PER UNIT, keyed by wheatUnitKey — options are a property of the coverage, not the farm.
  // Martin carries TA on his conventional Monroe wheat and YA on his organic Monroe wheat;
  // one farm-wide object would have applied whichever unit was seeded last to both.
  const [farmOpts, setFarmOpts] = useState({});          // unit key -> { ta, ya, yc, ye, mc }
  const FARM_OPTS_OFF = { ta:false, ya:false, yc:false, ye:false, mc:false, pf:false };
  // Practice override, unit key -> code. Lets the agent quote a prospect as organic, or
  // correct a unit whose practice could only be inferred from the lossy label.
  const [pracOv, setPracOv] = useState({});
  // PLANNED ACRES, unit key -> string. The quote basis for a unit RMA has no reported acreage
  // for: new ground, a county elected but never planted, or an APH database built entirely of
  // T-yields. 19 of 213 wheat units are in that state (16 farms — Metzler Harford, My-T Acres
  // Livingston + Monroe, Marquart Cattaraugus + Genesee, Clark Five Blair + Centre …), and
  // every one of them rendered a card of dashes because every quote was gated on `acres`.
  //
  // IT IS ONLY EVER THE PREMIUM THAT NEEDS THIS. Measured across 6 counties x 3 unit
  // structures x 5 coverage levels, plus both area endorsements: the MPCI guarantee (bu/ac),
  // the liability ($/ac) and SCO/ECO coverage ($/ac) are ALL acre-INVARIANT — identical at 1
  // acre and at 1,000. Acres enter the rating in exactly one place, `discFactor`'s
  // BU/EU discount band, so they move the net premium and nothing else (worst case measured:
  // 14.4% across the whole 1–1,000 acre range, Metzler Harford $5.00 -> $4.37/ac).
  // So the guarantee is quoted with NO acreage assumption at all, and the premium is quoted
  // only against a figure a human typed. Nothing here defaults, guesses or carries a number
  // forward from another county — an invented acreage would be an invented premium.
  const [plannedAc, setPlannedAc] = useState({});
  // ── A UNIT STRUCTURE THE AGENT STATES (BUG-088 follow-up) ───────────────────
  // 80 of 127 CY2027 wheat units carry no unit structure, and the printed sheet
  // refuses to price one — rightly, because normUnit's fallback is ENTERPRISE, the
  // cheapest RMA sells, so a silent default understates a Basic grower's premium by
  // about $11/ac (McIlroy Wyoming, 75% RP: EU $10.43 vs BU $21.35). Measured
  // 2026-09-21 and the reason this map exists: refusing is right for a GUESS and
  // wrong for a FACT the agent holds. A structure stated here prices and prints,
  // labelled "entered" rather than as something the AIP keyed; unstated still
  // refuses. Same rung as the typed acreage, and the same farm-scoped key.
  const [unitEntry, setUnitEntry] = useState({});   // wheatPlannedKey -> "OU" | "BU" | "EU"
  // Options SEED FROM THE PRIOR YEAR and stay editable — a renewal should start from what
  // he already carries, not from blank. Seeded once per county (elections differ by
  // county); `optSeeded` records which county was seeded so switching tabs re-seeds but a
  // change the agent just made is never stomped by a re-render.
  const [optSeeded, setOptSeeded] = useState(null);   // unit key already seeded
  // Per-unit APH editor: type in this year's (2026) production / edit any year → recompute approved yield.
  // Keyed by county. rows = [{year, y(bu/ac)}]; open = editor expanded; applied = feed recompute into the quote.
  // ⚠ `aphEdit` IS FARM-SCOPED — it lives in the BUG-078 store below, not here. See there.
  // Editable recommendation column (per county): MPCI coverage level + unit, and whether
  // SCO/ECO are stacked. ⚠ Read out of the farm-keyed store below (BUG-078): its own key is
  // COUNTY|PRACTICE, which two growers in one county share.
  const REC_DEFAULT = { cl: 0.85, unit: "EU", sco: true, eco: true };
  // ── THE AREA REFERENCE — RMA's COUNTY history, not NASS state yield ─────────
  //
  // Until 2026-09-01 this loaded NASS wheat yield with `county=is.null` and
  // correlated the farm against the STATE. SCO and ECO do not settle on the
  // state; they settle on the RMA county index, which is what `sco_eco_county`
  // holds (28 NY counties / 57 PA / 77 OH / 23 MD, 2017-2025 settled).
  //
  // Measured over the 198-unit wheat book before the switch: |r_state -
  // r_county| has a median of 0.19 and exceeds 0.30 on 53 of the 167 units where
  // both resolve, and the recommendation changes on 55 of 198 units / 6,056
  // acres. It is not a refinement — on 28% of the book the old rule chose a
  // coverage level by how the farm tracked an average that pays nobody.
  //
  // Rows go to `areaFit` RAW. It does its own state/county filtering and its own
  // settled-year test, so the page cannot disagree with the checker about which
  // observations counted.
  const [areaRows, setAreaRows] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await sbFetch("sco_eco_county", {
          filters: ["crop=eq.WHEAT", "state=in.(NY,PA,MD,OH)"],
          // ⚠ projected_price / harvest_price ARE LOAD-BEARING, not decoration. The
          // coverage recommendation prices each 5-point band as REVENUE protection, and
          // RP's harvest-price provision raises the effective yield trigger to y < L/h
          // whenever the harvest price falls. Drop these two columns and bandRules gets
          // h = 1 on every year, silently reverting to a yield-only test — measured at
          // $2.29/ac on the 75–80 band, and enough to move the 85% recommendation from
          // 4 units to 13 across the book.
          select: "crop_year,crop,state,county,expected_yield,rma_final_yield,revenue_ratio,sco_payout_pct,eco90_payout_pct,eco95_payout_pct,projected_price,harvest_price",
        });
        if (alive) setAreaRows(rows || []);
      } catch { /* the fit reports INSUFFICIENT_HISTORY and says why */ }
    })();
    return () => { alive = false; };
  }, []);
  // Batch print: check farms from the list → fetch each → stack their county pages into one document.
  // Selection state lives in the parent (see the note on this component's props).
  const [batchQ, setBatchQ] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  // APH builder: enter raw yield history + elect TA/YA/YC/YE → recomputed approved yield feeds qi.aph
  const [aphb, setAphb] = useState({ on:false, ta:false, ya:false, yc:false, ye:false, prior:"", taFactor:"",
    rows:[{ year:2026, y:"" },{ year:2025, y:"" },{ year:2024, y:"" },{ year:2023, y:"" }] });
  const aphRefs = useMemo(() => wheatYieldRefs2027(qi.state, qi.county, qi.practice), [qi.state, qi.county, qi.practice]);
  // A BLANK QUOTE FOLLOWS THE COUNTY. RMA's T-yield is what it assigns a farm with no
  // history, and it is published for all 1,104 cells this quoter covers (measured), so
  // there is no county where this leaves the box empty. It stops the moment somebody
  // types: their number is theirs, and a later county change must not take it away.
  React.useEffect(() => {
    if (qi.aphTouched || aphRefs.tYield == null) return;
    setQi(s2 => (s2.aphTouched || String(s2.aph) === String(aphRefs.tYield)
      ? s2 : { ...s2, aph: String(aphRefs.tYield) }));
  }, [aphRefs.tYield, qi.aphTouched]);
  const aphCalc = useMemo(() => {
    if (!aphb.on) return null;
    // FILTER THE EDITOR ROWS, THEN MAP. Filtering after the map tests `r.y` on an object
    // that only has `year` and `yield` — undefined, so `+undefined > 0` is false and EVERY
    // row is dropped. See the editor call site for what that cost.
    const ys = aphb.rows.filter(r => +r.year && +r.y > 0).map(r => ({ year:+r.year, yield:+r.y }));
    if (!ys.length) return null;
    return computeApprovedYield2027({ state:qi.state, county:qi.county, practice:qi.practice, yields:ys, ta:aphb.ta, ya:aphb.ya, yc:aphb.yc, ye:aphb.ye, bfr: !!qi.bfr, taFactor: aphb.taFactor ? +aphb.taFactor : null, priorApproved: aphb.prior ? +aphb.prior : null });
  }, [aphb, qi.state, qi.county, qi.bfr, qi.practice]);
  useEffect(() => { if (aphb.on && aphCalc && aphCalc.approved != null) setQi(s => (s.aph === String(aphCalc.approved) ? s : { ...s, aph: String(aphCalc.approved) })); }, [aphb.on, aphCalc]);

  // Dedupe the book by farm name — one grower holds several policy cards (Sullivan has 4).
  // Combine every policy id + number under the name so ONE pick pulls the whole operation;
  // the crop selector then narrows the view to just that crop's units.
  const farms = useMemo(() => {
    const g = {};
    for (const f of (farmers || [])) {
      const key = (f.name || "").trim(); if (!key) continue;
      const e = g[key] || (g[key] = { name: f.name, state: f.state || "NY", allPolicyIds: [], policyNums: [], farmerIds: [] });
      (f.allPolicyIds || f._allPolicyIds || []).forEach(id => { if (id != null && !e.allPolicyIds.includes(id)) e.allPolicyIds.push(id); });
      (f._allFarmerIds || (f.id != null ? [f.id] : [])).forEach(id => { if (id != null && !e.farmerIds.includes(id)) e.farmerIds.push(id); });
      const pn = f.policyNumber; if (pn && !e.policyNums.includes(pn)) e.policyNums.push(pn);
    }
    return Object.values(g);
  }, [farmers]);

  const farmer = sel;

  // ── EVERY WHEAT CUSTOMISATION, KEYED BY THE FARM IT WAS SET ON (BUG-078) ────
  // One store, so the coverage override, the stack selection and the print sections
  // cannot drift apart about which grower they belong to. Reads resolve through
  // `wheatCustomFor`, so the moment `farmer` changes the previous farm's settings are
  // already unreachable — no effect, no frame in which the new grower's card is drawn
  // from the old grower's overrides. Writes are ignored with no farm selected.
  //
  // Every setter below keeps the SHAPE its call sites already use (`setX(prev => …)`),
  // so nothing downstream had to change to become farm-safe.
  const [wheatCustom, setWheatCustom] = useState({});   // farm key -> { rec, bars, sections }
  const wheatFarmK = wheatFarmKey(farmer);
  const wcust = wheatCustomFor(wheatCustom, wheatFarmK);
  const recCfg = wcust.rec, barSel = wcust.bars, printSec = wcust.sections;
  const patchWheatCustom = (fn) => setWheatCustom(all => {
    if (!wheatFarmK) return all;
    const base = wheatCustomFor(all, wheatFarmK);
    return { ...all, [wheatFarmK]: { ...base, ...fn(base) } };
  });
  const setRecCfg = (up) => patchWheatCustom(b => ({ rec: typeof up === "function" ? up(b.rec) : up }));
  const setBarSel = (up) => patchWheatCustom(b => ({ bars: typeof up === "function" ? up(b.bars) : up }));
  const setPrintSec = (up) => patchWheatCustom(b => ({ sections: typeof up === "function" ? up(b.sections) : up }));
  // ⚠ THE APH EDIT AND THE DATABASE TICKS WERE LEFT OUT OF BUG-078, AND THEY MOVE MONEY.
  // Both are keyed COUNTY|PRACTICE — farm-agnostic — so an APH recomputed on one grower's
  // LIVINGSTON|003 priced the next grower's LIVINGSTON|003 card (19 farms share that key in
  // the CY2027 book), and database ticks carried across the same way. That stayed an
  // on-screen hazard only while the printed quote ignored both; the print now honours
  // them (resolveUnitAph), so they have to belong to the farm they were set on first.
  // Same store, same read-time scope, same setter shapes as the three above.
  const aphEdit = wcust.aph || WHEAT_CUSTOM_EMPTY.aph;
  const unitSel = wcust.units || WHEAT_CUSTOM_EMPTY.units;
  const setAphEdit = (up) => patchWheatCustom(b => ({ aph: typeof up === "function" ? up(b.aph || {}) : up }));
  const setUnitSel = (up) => patchWheatCustom(b => ({ units: typeof up === "function" ? up(b.units || {}) : up }));
  // ESTIMATED PLANTED ACRES PER APH DATABASE for the quote year (Mike, 2026-09-18). Keyed
  // COUNTY|PRACTICE -> { "0003-0002": "60" }, in the same farm-keyed store for the same reason.
  const unitAc = wcust.unitAc || WHEAT_CUSTOM_EMPTY.unitAc;
  const setUnitAc = (up) => patchWheatCustom(b => ({ unitAc: typeof up === "function" ? up(b.unitAc || {}) : up }));
  // What a print of the LOADED farm hands resolveUnitAph: its APH databases and the agent's
  // own edit + ticks, both already scoped to this farm above. The batch printer builds its
  // own with `agent: null`.
  const loadedAphCtx = () => ({ units: covUnits, agent: { edits: aphEdit, sels: unitSel, unitAc } });
  // `autoLv` is the automatic set as it stands RIGHT NOW, and it seeds the selection on the
  // first tick — so the agent starts from what the chart already shows instead of from
  // nothing, and the first click reads as "remove this one" rather than "throw it all away".
  const toggleBarLevel = (unitKey, cl, autoLv) => setBarSel(m => {
    const k = (unitKey || "").toUpperCase();
    const cur = m[k] || autoLv || [];
    const has = cur.some(x => Math.abs(x - cl) < 1e-9);
    const next = has ? cur.filter(x => Math.abs(x - cl) >= 1e-9) : [...cur, cl];
    return { ...m, [k]: next.slice().sort((a, b) => a - b) };
  });
  // Back to the engine's own choice: DELETE the key rather than write the automatic set
  // into it, or the selection freezes and stops tracking a level change made afterwards.
  const resetBarLevels = (unitKey) => setBarSel(m => { const n = { ...m }; delete n[(unitKey || "").toUpperCase()]; return n; });
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase(); if (!s) return [];
    return farms.filter(f => f.name.toLowerCase().includes(s) || f.policyNums.join(" ").toLowerCase().includes(s))
      .sort((a, b) => a.name.localeCompare(b.name)).slice(0, 40);
  }, [farms, q]);

  useEffect(() => {
    if (!farmer) { setCov([]); setPh([]); return; }
    let alive = true; setLoading(true);
    (async () => {
      const pids = (farmer.allPolicyIds || farmer._allPolicyIds || []);
      if (!pids.length) { if (alive) { setCov([]); setPh([]); setLoading(false); } return; }
      const flt = pids.join(",");
      try {
        const [c, p, pol, ov, u, ar] = await Promise.all([
          sbFetch("crop_coverage", { filters:[`policy_id=in.(${flt})`], select:"policy_id,crop,county,type,practice,practice_code,aph_yield,rate_yield,adjusted_yield,yield_options,insurance_options,coverage_level,plan,price_election,price_election_pct,acres,liability,net_premium,total_premium,subsidy,admin_fee_waiver_code,unit_structure,mpci_plan_code,iif_key" }),
          sbFetch("production_history", { filters:[`policy_id=in.(${flt})`], select:"crop,county,type,practice,crop_year,acres,yield_per_acre,record_type,excluded_from_avg" }),
          sbFetch("policies", { filters:[`id=in.(${flt})`], select:"id,state,policy_number,crop_year" }),
          sbFetch("wheat_premium_overrides", { filters:["crop_year=eq.2026"], select:"policy_number,county,plan,coverage_level,unit_structure,net_per_acre,gross_per_acre,coverage_per_acre" }).catch(() => []),
          // THE APH DATABASES BEHIND THE SUMMARY. crop_coverage carries one averaged row per
          // county; RMA approves and rates each database separately, and a farm can hold a
          // dozen. Kennedy Farms' Livingston wheat is THIRTEEN (SECTION 1..13 on his APH
          // printout), approved 50 to 76 — and eleven of them show 0Z for last year because
          // he only plants wheat on two of those sections. Averaging all thirteen unweighted
          // gives dormant ground the same vote as the ground he farms.
          sbFetch("crop_coverage_units", { filters:[`policy_id=in.(${flt})`], select:"policy_id,county,crop,plan,practice_code,unit_number,sub_unit,acres,aph_yield,rate_yield,adjusted_yield,yield_options,is_prevent_plant" }).catch(() => []),
          // The year records BEHIND each database. Empty until a sync has run since this
          // table shipped — the panel degrades to the unit's approved/rate/adjusted alone,
          // which is still the right grain, just without the history that explains it.
          // ⚠ policy_number, state and type_code are SELECTED for the level optimizer,
          // which groups a database by its policy STEM — the CY2026 and CY2027 copies of
          // one database differ only by the `-YY` suffix, so without the number they
          // group as two databases and every year is counted twice. A rule reading a
          // column its own query never selected is the defect check-query-contract exists
          // for; the panel below it degrades to the same columns it always read.
          sbFetch("aph_database_rows", { filters:[`policy_id=in.(${flt})`], select:"policy_id,policy_number,crop,county,state,type_code,practice,unit_number,sub_unit,yield_key,crop_year,yield_per_acre,acres,production,record_type,production_status" }).catch(() => []),
        ]);
        // THE AIP'S OWN ACREAGE RECORDS, read for the unit structure. crop_coverage stamps it only
        // where the AIP wrote acreage that year, so a county the grower did not plant last year
        // carries none — and normUnit() used to turn that blank into Enterprise. DU MOND AG LLC's
        // Wayne wheat was quoted at enterprise-unit rates while its last planted record (CY2025)
        // says BASIC unit. Admin-only table: a session that cannot read it gets no rows, and the
        // unit stays whatever the coverage row states, or unknown.
        const wIifs = [...new Set((c || []).filter(x => x.crop === "WHEAT" && x.iif_key).map(x => String(x.iif_key)))];
        const aa = wIifs.length ? await sbFetch("m13_acreage", { filters: [`iif_key=in.(${wIifs.join(",")})`],
          select: "aip,crop_year,iif_key,unit_structure,non_premium_acreage_code,reported_acreage" }).catch(() => []) : [];
        if (alive) { setAipAcre(aa || []); setCov(c || []); setPh(p || []); setOvr(ov || []); setCovUnits(u || []); setAphRows(ar || []);
          setPolStates(Object.fromEntries((pol||[]).map(x => [String(x.id), (x.state||"").toUpperCase()])));
          setPolNumbers(Object.fromEntries((pol||[]).map(x => [String(x.id), x.policy_number || ""])));
          setPolYears(Object.fromEntries((pol||[]).map(x => [String(x.id), x.crop_year != null ? +x.crop_year : null]))); }
      } catch { if (alive) { setAipAcre([]); setCov([]); setPh([]); setOvr([]); setCovUnits([]); setAphRows([]); setPolStates({}); setPolNumbers({}); setPolYears({}); } }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [farmer]);

  // Per-policy state (policies.state is authoritative; farmers.state is null in the book).
  const st = (farmer && farmer.state) || "NY";
  const unitState = c => (polStates[String(c && c.policy_id)] || st || "NY").toUpperCase();
  // EVERY state the loaded coverage is written in, in a stable order. `st` is a single
  // first-wins value and a farm is not obliged to sit in one state: Metzler is MD + PA.
  // Falls back to `st` only while nothing is loaded, so the header never renders blank.
  const farmStates = (() => {
    const out = [];
    for (const c of cov) { const u = unitState(c); if (u && !out.includes(u)) out.push(u); }
    return out.length ? out.sort() : [st];
  })();
  // THE STATE IS THE ROW'S OWN, NOT THE FARM'S. `st` is whichever policy card the farms
  // memo happened to iterate first, so on a farm spanning states it is arbitrary — Craig
  // Metzler holds MD and PA and it resolved to PA. Keyed on that, his MARYLAND Harford
  // county was looked up as `PA|HARFORD`, matched nothing, and printed "—" for both county
  // expected yield and basis: a WRONG-STATE LOOKUP rendering as missing data. The silent
  // direction is worse — a county name that exists in both states (FRANKLIN, MONROE, WAYNE,
  // ADAMS, ERIE and a dozen more are in two or three of the five wheat states) would have
  // returned the OTHER state's expected yield as a fact. Callers pass the unit's own state.
  const countyExp = (crop, county, unitSt) => {
    const k = ((unitSt || st) || "NY").toUpperCase() + "|" + (county || "").toUpperCase();
    const c = ((se[2026] || {})[crop] || {})[k] || ((se[2025] || {})[crop] || {})[k];
    return c && c.expected_yield != null ? +c.expected_yield : null;
  };
  // per-year acre-weighted yield series for a crop+county from ANY production-history rows (pure).
  // `prac` splits the history by PRACTICE, and it matters as much as splitting by crop type:
  // Jeffres farms Wyoming wheat both ways and 2023 came in at 136.7 bu conventional against
  // 76.0 organic. Acre-weighting them together produced ~114 — a yield that is on neither
  // unit and belongs to no APH database — and that blend was seeding the APH editor, the CV
  // and the farm-area correlation. Same rule as never averaging across crop types.
  //
  // production_history carries only the practice LABEL (no practice_code column), so the
  // split is organic vs not — which is exactly the axis the yields differ on. A NULL label
  // counts as non-organic, matching how the syncs write conventional rows.
  const yieldSeriesOf = (rows, crop, county, prac) => {
    const byYr = {};
    const wantOrg = prac ? isOrganicPractice(prac) : null;
    for (const r of (rows || [])) {
      if (r.crop !== crop || (r.county || "").toUpperCase() !== (county || "").toUpperCase()) continue;
      if (wantOrg !== null && /ORGANIC/.test(String(r.practice || "").toUpperCase()) !== wantOrg) continue;
      // AN APH DATABASE IS NOT A YIELD AVERAGE, and this is the one place that difference
      // decides money. `excluded_from_avg` is the right filter for the analytics screens —
      // you do not want transitional yields in "what does this farm actually grow" — but RMA
      // counts a T (transitional) or PP record as one of the ten, and only Z (Zero Planted
      // Acres) leaves the database. Verified on Kennedy Farms' CY2027 APH printout: unit 0009
      // is 70T, 70T, 63, 43 and RMA approves 62 = 61.5 rounded, dividing by FOUR; unit 0011
      // counts a 35PP the same way. Excluding the T did not shorten his database, it slid the
      // ten-year window back and pulled a 89.2 in from 2016 — 4.2 bu of guarantee that is not
      // his. Note the printout's "# of Years" counts ACTUALS only and is never the divisor.
      const rt = String(r.record_type || "").toUpperCase();
      const aphRecord = rt === "T" || rt === "PP" || rt === "PW";
      if (r.excluded_from_avg === true && !aphRecord) continue;
      const y = r.yield_per_acre != null ? +r.yield_per_acre : null; if (y == null || y <= 0) continue;
      const ac = r.acres != null && +r.acres > 0 ? +r.acres : 0;
      const Y = byYr[r.crop_year] || (byYr[r.crop_year] = { wy:0, wa:0, sy:0, n:0 });
      if (ac) { Y.wy += y * ac; Y.wa += ac; } else { Y.sy += y; Y.n++; }
    }
    return Object.keys(byYr).map(Number).sort().map(yr => { const Y = byYr[yr]; return { yr, y: Y.wa > 0 ? Y.wy / Y.wa : (Y.n > 0 ? Y.sy / Y.n : null) }; }).filter(o => o.y != null);
  };
  const yieldSeries = (crop, county, prac) => yieldSeriesOf(ph, crop, county, prac);   // current-farm convenience

  // The fallback acreage out of the APH history is `wheatHistAcres` at module scope —
  // ONE rule, already read by the card and the printed quote. It is what now lets the
  // coverage recommendation run before this year's acres exist; see the note there.
  const wheatHistAcresFor = (county) => wheatHistAcres(ph, county);

  // ── ACRE-WEIGHTED APH ACROSS THE DATABASES ────────────────────────────────────
  // RMA approves and rates each APH database separately and the liabilities SUM —
  // proved on Kennedy's 2026: unit 0007 (57 bu x 240.52 ac -> $59,295) plus unit 0008
  // (59 x 149.26 -> $38,087) is his $97,382 coverage total to the dollar. The summary
  // row can only report "average of N", and it averages UNWEIGHTED, which gives every
  // database an equal vote no matter how much ground sits behind it.
  //
  // That is not a rounding difference on a farm like his. His Livingston wheat is
  // THIRTEEN databases (SECTION 1..13, approved 50 to 76) and he plants wheat on TWO
  // of them — the other eleven read 0Z on last year's APH page. Unweighted they average
  // 67.4; weighted by the ground he actually farms they come to 57.8, which is why NAU
  // quotes him at 57 and we were quoting 67.
  //
  // THIS YEAR'S DATABASES CARRY NO ACRES until the acreage report (November for fall
  // wheat), so weight by LAST YEAR'S acres for the same unit+sub-unit — Kevin's rule,
  // 2026-08-10: last year's acres as a starting point, a rough estimate. A database
  // that was dormant last year therefore weighs nothing, which is right for the common
  // case and wrong the year he moves wheat onto new ground; the page says which basis
  // it used so that is visible rather than silent. Falls back to this year's own acres
  // where they exist, and to the plain average when no acres can be found at all.
  //
  // ⚠ THE RULES THEMSELVES LIVE IN src/wheat/aphDatabases.js NOW (2026-09-18). They were
  // closures here over `covUnits`, which is why the PRINTED quote never had them: the
  // print builder walks farms it was handed as arguments, never received the database
  // rows, and so priced every CY2027 unit on the unweighted summary — Clark Five's
  // Huntingdon wheat at 75.0 on paper against 98.7 on this card. These three names are
  // kept as thin wrappers so the panel below reads exactly as it did.
  const polYearOf = u => { const y = polYears ? polYears[String(u && u.policy_id)] : null; return y == null ? null : +y; };
  const unitDbsFor = (county, prac, curYear, planned = null) => unitDatabases(covUnits, polYearOf, { county, practice: prac, curYear, planned });
  const blendDbs = blendDatabases;
  const weightedAphFor = (county, prac, curYear) => weightedDatabaseAph(covUnits, polYearOf, { county, practice: prac, curYear });

  // ── THE APH A UNIT IS PRICED ON — ONE LADDER, THE CARD AND EVERY PRINTED PAGE ──
  // The card resolved this inline and the print builder read `r.aph_yield`, so the two
  // could not agree on any unit where the ladder does anything — which on CY2027 wheat
  // is 20 county-units across 18 farms, measured 2026-09-18 (Clark Five 75.0 printed vs
  // 98.7 on screen, Kennedy 67.4 vs 57.8, Shaffer 82.3 vs 91.7). Both call this now.
  //
  // `units`/`years`/`phRows` are the FARM'S OWN data, passed in rather than read off
  // component state, because the batch printer walks many farms through one builder.
  // `edit` and `sel` are the agent's on-screen APH edit and database ticks for this unit;
  // a caller that cannot vouch they belong to this farm (the batch printer) passes none.
  //
  // WHOSE APPROVED YIELD IS THIS? The AIP re-approves every crop year, and the roll
  // happens at the AIP, not here: Koch Libra's Livingston wheat went 75 to 72 when his
  // 2026 claim year (24.5 bu/ac, production_status M) landed on the database and the
  // oldest year fell off. Where a current-year row exists we quote that year's databases;
  // where it does not, we are on LAST YEAR'S approved yield and the page has to say so.
  //
  // ROLL THE DATABASE FORWARD ONCE LAST YEAR IS KEYED. Kevin, 2026-08-07: "If we dont
  // have 2026 production keyed yet, just use existing 2016-2025 database, but when 2026
  // production is keyed, update to use that." That recomputation is OURS, not the AIP's,
  // so it is labelled as ours; a manual edit in the APH database editor outranks it.
  //
  // FILTER FIRST, THEN MAP on an edited history — the reverse silently threw the whole
  // database away and computeApprovedYield2027 fell through to the county T-yield
  // (Kennedy Livingston printed 76.0 against his own 69.8, found by Kevin 2026-08-10).
  // YC is a floor at 90% of LAST YEAR'S approved yield, so the edit path passes it too.
  //
  // RMA prices off THREE yields (P11-1 §3/§13): approved (guarantee), RATE (base rate)
  // and ADJUSTED (effective coverage level). The AIP's own values are preferred; they
  // are DERIVED only when absent or edited, and that derivation cannot reproduce a YA
  // grower's rate yield — hence the order. RATE excludes YA; ADJUSTED includes it. The
  // adjusted yield falls back to the APPROVED yield, never the rate yield: defaulting to
  // the rate yield would invent a TA for every YA grower and over-rate them.
  const resolveUnitAph = (r, { units, years, phRows, fips, prac, fo, edit, sel, planned = null }) => {
    const county = (r.county || "").toUpperCase();
    const storedAph = +r.aph_yield;
    const yearOf = u => { const y = years ? years[String(u && u.policy_id)] : null; return y == null ? null : +y; };
    const aphCropYear = yearOf(r);
    const aphIsPriorYear = aphCropYear != null && aphCropYear < MPCI_WHEAT_YEAR;
    const series = yieldSeriesOf(phRows, "WHEAT", county, prac);
    const aphYrs = series.map(o => o.yr).sort((a, b) => a - b);
    const aphNewest = aphYrs.length ? aphYrs[aphYrs.length - 1] : null;
    const aphStale = aphNewest != null && aphNewest < MPCI_WHEAT_YEAR - 1;
    const F = fo || FARM_OPTS_OFF;
    const edited = !!(edit && edit.applied);
    const rollCalc = (aphIsPriorYear && aphNewest === MPCI_WHEAT_YEAR - 1 && !edited)
      ? computeApprovedYield2027({ state: fips, county, practice: prac,
          yields: series.map(o => ({ year: o.yr, yield: o.y })),
          ta: F.ta, ya: F.ya, yc: F.yc, ye: F.ye, priorApproved: storedAph })
      : null;
    const rolled = rollCalc && rollCalc.approved != null && Math.abs(rollCalc.approved - storedAph) > 0.05
      ? rollCalc.approved : null;
    const editRows = edited ? (edit.rows || []).filter(x => +x.year && +x.y > 0).map(x => ({ year:+x.year, yield:+x.y })) : null;
    const edCalc = edited ? computeApprovedYield2027({ state:fips, county, practice:prac, yields: editRows,
      ta:F.ta, ya:F.ya, yc:F.yc, ye:F.ye, priorApproved: storedAph }) : null;
    // `planned` — acres typed per database for the quote year; they outrank last year's as the
    // weights (see unitDatabases), and their sum is the enterprise acreage the premium is banded on.
    const dbOpts = { county, practice: prac, curYear: aphCropYear, planned };
    const wtd = weightedDatabaseAph(units, yearOf, dbOpts);
    // Selection is a SET, so all three modes are one mechanism: none chosen is the whole
    // operation, one is that database, several is those several blended.
    const selKeys = sel || [];
    const selDbs = selKeys.length ? unitDatabases(units, yearOf, dbOpts).filter(d => selKeys.includes(d.k)) : [];
    const selBlend = selDbs.length ? blendDatabases(selDbs) : null;
    const res = resolveQuoteYields({ stored: { aph: storedAph, rate: r.rate_yield, adjusted: r.adjusted_yield, yieldOptions: r.yield_options },
      edited: edCalc && edCalc.approved != null ? { aph: edCalc.approved } : null, editApplied: edited,
      selected: selBlend, weighted: wtd, rolled });
    const aph = res.aph;
    const derive = ya => {
      const ser = edited ? editRows : series.map(o => ({ year:o.yr, yield:o.y }));
      if (!ser.length) return null;
      const c = computeApprovedYield2027({ state:fips, county, practice:prac, yields:ser, ta:false, ya, yc:F.yc, ye:false });
      return (c && c.approved != null) ? Math.min(c.approved, aph) : null;
    };
    const rateY = res.rate ?? derive(false);
    const adjY = res.adjusted ?? (F.ya ? derive(true) : null) ?? aph;
    // THE MPCI QUOTE FOR THIS UNIT. Where the APH is a blend of databases that carried acres,
    // each database is quoted on its own three yields and combined (quoteOverDatabases) — the
    // way RMA rates it and the AIP bills it. An edited history is ONE database by construction.
    const dbQuote = !edited && res.blend && (res.source === "weighted" || res.source === "selected")
      ? quoteOverDatabases(res.blend.members, quoteWheatRP2027) : null;
    // WHICH §14 RATE-DIFFERENTIAL FORMULA (BUG-095). Above the top ADM coverage level RMA
    // adds a cubic surcharge when the APH database's own option list names QL, YE or YC, and
    // none when TA is elected alone. The list is the database's (P15 f[38], `yield_options`);
    // the coverage's ELECTION list is passed beside it only because a list naming none of the
    // three settles the question too. An applied edit is the agent's own history, rated on the
    // quoter's toggles — the same ones derive() and the edited APH used.
    const yieldOptions = edited ? ["ta", "ya", "yc", "ye"].filter((o) => F[o]).map((o) => o.toUpperCase()).join(",")
      : res.yieldOptions;
    const electedOptions = r.insurance_options ?? null;
    const quoteBase = dbQuote || quoteWheatRP2027;
    const quoteRP = (a) => quoteBase({ yieldOptions, electedOptions, ...a });
    return { county, storedAph, aphCropYear, aphIsPriorYear, aphYrs, aphNewest, aphStale, rollCalc, rolled,
             edited, edCalc, wtd, selDbs, selBlend, aph, rateY, adjY, derive, source: res.source, blend: res.blend,
             dbQuote, quoteRP, yieldOptions, electedOptions, plannedUnitAcres: plannedUnitAcres(units, yearOf, dbOpts) };
  };
  const cvOf = series => { const v = series.map(o => o.y); if (v.length < 3) return null; const m = v.reduce((a, b) => a + b, 0) / v.length; const va = v.reduce((a, b) => a + (b - m) * (b - m), 0) / (v.length - 1); return m > 0 ? Math.sqrt(va) / m * 100 : null; };
  const pearson = (a, b) => { const n = a.length; if (n < 4) return null; const ma = a.reduce((s,x)=>s+x,0)/n, mb = b.reduce((s,x)=>s+x,0)/n; let sab=0,saa=0,sbb=0; for (let i=0;i<n;i++){ const da=a[i]-ma, db=b[i]-mb; sab+=da*db; saa+=da*da; sbb+=db*db; } return (saa>0 && sbb>0) ? sab/Math.sqrt(saa*sbb) : null; };
  // `farmAreaCorr` lived here and correlated the farm against NASS STATE wheat
  // yield. It is DELETED, not kept as a fallback: SCO and ECO settle on the RMA
  // COUNTY index, so a state correlation answered a question nobody is buying
  // against, and leaving it reachable is how a surface quietly keeps using it.
  // `areaFit` in src/wheat/areaFit.js is the one rule now, and `pearson` above
  // stays only for callers outside the wheat recommendation.
  // How often the series fell below level×APH (individual downside frequency).
  const lossFreqAt = (series, aph, level) => { if (!series || series.length < 4) return null; const t = level * aph; return series.filter(o => o.y < t).length / series.length; };

  // Endorsement classifiers. ARPI (Area Risk Protection) is how SCO is stored on some policies — treat it as SCO.
  const isEndorsement = pl => /SCO|ECO|ARPI/i.test(pl || "");
  const isScoPlan = pl => /SCO|ARPI/i.test(pl || "");
  const isEcoPlan = pl => /ECO/i.test(pl || "");
  // group MPCI coverage rows into crops → units (county/type/practice)
  const byCrop = useMemo(() => {
    const g = {};
    for (const c of cov) {
      if (isEndorsement(c.plan)) continue;                         // SCO/ECO/ARPI handled separately
      if (c.aph_yield == null && c.acres == null) continue;
      (g[c.crop || "?"] ||= []).push(c);
    }
    return g;
  }, [cov]);
  const endorsements = useMemo(() => cov.filter(c => isEndorsement(c.plan)), [cov]);

  // ── BEGINNING FARMER / RANCHER, DERIVED — see src/bfrStatus.js ─────────────
  // M13 states no BFR field. What it does state is the SUBSIDY, and SCO/ECO carry a
  // flat 80% regardless of level or unit structure, so an elevation above it is the
  // bump and nothing else. Per POLICY, never per farm: Howlett holds one standard
  // policy and one BFR policy under a single name (a generational transition, Kevin
  // 2026-09-03), and a farm-level rollup would report one of them as the answer.
  const bfrByPolicy = useMemo(
    () => BFR.deriveBfr(cov, { bookRows: cov, cropYear: MPCI_WHEAT_YEAR - 1 }),
    [cov]);

  // Auto follows the derivation; the other three are the agent overriding it, which
  // they must be able to do in both directions — a customer who has just transitioned
  // will not show a bump on last year's policy, and one whose 10 crop years are up
  // still shows one. "auto" is not a claim that the derivation is right, it is a claim
  // that nobody has said otherwise.
  const [bfrMode, setBfrMode] = useState("auto");
  // BFR IS PER POLICY, AND policy_id CHANGES EVERY CROP YEAR — see BFR.bfrByStem. The
  // county's own prior-year row is NULL for a county the grower added this year, which
  // is why the stem index exists and why the row being quoted is passed as fallbackRow.
  const bfrStemIdx = useMemo(() => BFR.bfrByStem(bfrByPolicy, (pid) => polNumbers[String(pid)]), [bfrByPolicy, polNumbers]);
  const bfrOptOf = (row, fallbackRow) => {
    // One rule, shared with the PRINT path — see BFR.bfrModeOption. These were two
    // copies and only this one knew about the override, so the screen moved and the
    // document did not.
    const ov = BFR.bfrModeOption(bfrMode);
    if (ov) return ov;
    const st = BFR.bfrStatusFor(bfrByPolicy, bfrStemIdx, row, fallbackRow, (pid) => polNumbers[String(pid)]);
    // The observation is LAST year's policy; the quote is NEXT year's. The durable +10
    // carries forward, the first-two-years +5 does NOT — it expires, and nothing in the
    // record says whether the quote year is still inside it. Seeding it forward would
    // under-quote a grower on a document they sign.
    const seed = BFR.bfrQuoteSeed(st, { observedYear: MPCI_WHEAT_YEAR - 1, quoteYear: MPCI_WHEAT_YEAR });
    return { bfr: seed.bfr, bfrFirstTwoYears: seed.firstTwoYears, bfrBenefitYear: seed.benefitYear,
             bfrSteppedDown: seed.steppedDown, bfrQuotedTier: seed.quotedTier, bfrObservedTier: seed.observedTier,
             // How the status was established, carried so the printed sheet can say WHERE
             // it came from rather than only that it was applied. STATED_* is the AIP's
             // own waiver code; anything else is this engine's inference off the subsidy.
             bfrMethod: st.method || null };
  };
  // What the derivation SAW, for the badge. Distinct from what is being QUOTED, which
  // is bfrOptOf above — the two differ whenever the +5 has been dropped on carry-forward
  // or the agent has overridden, and a badge that hid that would be the disagreement.
  const bfrSeen = useMemo(() => {
    const tiers = new Map();
    for (const [pid, st] of bfrByPolicy) if (st.tier === BFR.TIER.BFR_10 || st.tier === BFR.TIER.BFR_15) tiers.set(pid, st);
    return tiers;
  }, [bfrByPolicy]);
  // ⚠ A SIBLING POLICY THAT DISAGREES IS A FACT THE AGENT NEEDS, NOT A VALUE TO APPLY.
  // BFR is per policy and this farm can hold both answers at once; the badge states the
  // other policy's reading so "not indicated" cannot be mistaken for "we have no idea",
  // and deliberately does not act on it — the other policy is a different insured.
  const bfrSibling = useMemo(() => {
    for (const [pid, st] of bfrSeen) {
      const crops = [...new Set((cov || []).filter((c) => String(c.policy_id) === String(pid))
        .map((c) => c.crop).filter(Boolean))].join(" + ");
      return { policyId: pid, policyNumber: polNumbers[String(pid)] || "another policy",
               crops: crops || "another crop", tier: st.tier, why: st.why };
    }
    return null;
  }, [bfrSeen, cov, polNumbers]);

  const cvColor = cv => cv == null ? "#8a8f8a" : cv < 15 ? "#1f7a3d" : cv < 25 ? "#b8860b" : "#a5461b";
  const basisColor = b => b == null ? "#8a8f8a" : (b >= 0.9 && b <= 1.1) ? "#1a2e1a" : "#a5461b";
  const th = { padding:"6px 9px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".04em", color:"#677167", fontWeight:700, whiteSpace:"nowrap" };
  const td = { padding:"6px 9px", fontSize:12.5, borderTop:"1px solid #eef3ee" };
  const sect = { background:"#fff", border:"1px solid #d5e2d5", borderRadius:12, padding:isMobile ? "14px 12px" : "16px 18px", marginBottom:18 };
  const capCty = c => titleCaseName(c);
  const pracLbl = (t, p) => [t, p].filter(x => x && !/^0+$/.test(x)).join("·") || "—";

  // ── shared quoter grid ($/acre net premium, every option) ──
  const LEVELS = [0.50, 0.55, 0.60, 0.65, 0.70, 0.75, 0.80, 0.85];
  const $ac = n => n == null ? "—" : "$" + (Math.round(n * 100) / 100).toFixed(2);
  const normUnit = u => { const s = (u || "").toUpperCase(); if (s.startsWith("E")) return "EU"; if (s.startsWith("B")) return "BU"; if (s.startsWith("O")) return "OU"; return "EU"; };
  // opts carries the policy elections that change the numbers: { bfr }. Passed in,
  // not read off qi, because this grid also renders a real farm’s coverage (not just the
  // manual scenario) and must reflect THAT policy’s elections.
  function quoteGrid(state, county, aph, acres, price, vol, current, scoPct = 100, ecoPct = 100, opts = {}) {
    const gridBfr = !!opts.bfr;
    const prac = opts.practice || "003";
    // Unit and endorsement election come from the ONE coverage panel (opts), not from
    // grid-local state. There used to be FOUR places to change a coverage — this row, the
    // recommendation column, the options bar above the tabs and the "add a level" row —
    // and only some of them moved the bars.
    const gUnit = opts.unit || "EU";
    const gridSco = opts.sco !== false, gridEco = opts.eco !== false;
    // ⚠ THE GRID WAS THE ONLY QUOTE ON THIS CARD NOT PRICED ON THE GROWER'S RATE
    // YIELD, AND IT UNDERSTATED EVERY LEVEL BY ~20-26%.
    //
    // RMA rates off the RATE yield, not the approved yield: §13 makes the
    // effective coverage level `cl × approved ÷ rate`, so a grower carrying TA
    // (approved > rate) is rated ABOVE the level he elected. Omit `rateYield`
    // and the engine defaults it to `aph`, eff collapses back to cl, and the
    // premium comes out low — silently, at every level at once, so the column
    // still looks internally consistent.
    //
    // Measured on KAST FARMS (Orleans, APH 79 / rate yield 71, TA elected),
    // against the SAME engine the bars above this table already call:
    //
    //        this grid    the bars     short by
    //   75%    $8.61       $10.80       $2.19
    //   80%   $14.73       $18.50       $3.77
    //   85%   $26.28       $33.09       $6.81   = $3,124 on his 458.8 ac
    //
    // Both numbers were on ONE screen. 81 of the 131 CY2027 wheat units that
    // carry both yields have rate ≠ approved (62%), mean ratio 1.080, max
    // 1.345 — so this is the majority of the renewal book, not an edge.
    //
    // ⚠ `check-shared-rules` CANNOT SEE THIS: it counts POSITIONAL arguments and
    // quoteWheatRP2027 takes ONE options object, so a dropped key is invisible
    // to it — the same shape as the BFR/SCO-ECO omission this file records.
    //
    // The blank quoter passes neither and is CORRECT: there the APH is typed and
    // no rate yield exists, so defaulting rate to approved is the right answer.
    const gRateY = opts.rateYield != null ? +opts.rateYield : null;
    const gAdjY  = opts.adjustedYield != null ? +opts.adjustedYield : null;
    // THE SAME SPLIT THE QUOTE COLUMNS USE, for the same reason. `AC` below silently falls
    // back to ONE acre, which is a real number at the very top of the BU/EU discount band —
    // so a unit with no reported acreage filled this grid with premiums up to ~14% high while
    // the columns above it showed em-dashes. One card, two answers, and the grid was the one
    // that looked authoritative. Coverage is acre-invariant and still renders on every level;
    // the PREMIUM half is withheld until somebody supplies an acreage.
    const gridPrem = opts.premiumBasis !== false;
    // ⚠ THE BLANK QUOTE HANDS IN ITS OWN ROWS. `rowFor(cl)` is blankQuoteCoverage at that
    // level — the same call the builder panel above the grid and the printed sheet make —
    // so the three price one configuration once. Without it this grid priced the blank
    // quote on its own pinned election (Enterprise, RP, SCO+ECO) whatever the builder
    // said, and a Basic-unit quote sat above an Enterprise table on one screen. The farm
    // card passes nothing here and prices exactly as before.
    const rowFor = typeof opts.rowFor === "function" ? opts.rowFor : null;
    county = (county || "").toUpperCase();
    if (!wheatCountyInSet(state, county)) return <div style={{ color:"#a5461b", fontSize:12.5, padding:"8px 2px" }}>{capCty(county) || "That county"} isn't in the 2027 {WHEAT_STATES[state] || ""} wheat rate set.</div>;
    if (!wheatCountyInSet(state, county, prac)) return <div style={{ color:"#a5461b", fontSize:12.5, padding:"8px 2px" }}>RMA doesn't offer {WHEAT_PRACTICE_NAMES[prac] || prac} wheat in {capCty(county)} County.</div>;
    const A = +aph, AC = +acres || 1;
    if (!A || A <= 0) return <div style={{ color:"#677167", fontSize:12.5, padding:"8px 2px" }}>Enter an APH to quote.</div>;
    if (!(+price > 0)) return <div style={{ color:"#a5461b", fontSize:12.5, padding:"8px 2px" }}>No price election on file for this practice — enter one to quote.</div>;
    // bfr rides with the MPCI line below, or the same tick subsidises one row of the
    // stack and not the other two, and the total is neither.
    const area = (cl, end) => quoteAreaRP2027({ state, county, endorsement:end, baseCl:cl, aph:A, acres:AC, price:+price, vol:+vol, bfr:gridBfr, practice:prac, coveragePct: areaCoverageFractionOfPct(end === "ECO" ? ecoPct : scoPct) });
    const cur = current ? { cl: Math.round(current.cl * 100), unit: normUnit(current.unit) } : null;
    const tdG = { padding:"5px 8px", fontSize:12, borderTop:"1px solid #eef3ee", textAlign:"right" };
    const thG = { padding:"6px 8px", fontSize:10, textTransform:"uppercase", letterSpacing:".02em", color:"#677167", fontWeight:700, textAlign:"right", whiteSpace:"nowrap" };
    const num = n => n == null ? "—" : $ac(n);
    const shown = [...LEVELS].sort((a, b) => a - b);
    const sbtn = a => ({ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:5, cursor:"pointer", border:"1px solid " + (a ? "#2d6a2d" : "#d5e2d5"), background:a ? "#eaf3ea" : "#fff", color:a ? "#1f7a3d" : "#b7c2b7" });
    const unitLbl = { OU:"Optional", BU:"Basic", EU:"Enterprise" };
    return (
      <div>
        <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:560 }}>
          <thead>
            <tr>
              <th style={{ ...thG, textAlign:"left" }}></th>
              <th style={{ ...thG, textAlign:"center", color:"#2a3a2a", borderLeft:"2px solid #e4ede4" }} colSpan={2}>MPCI</th>
              {gridSco && <th style={{ ...thG, textAlign:"center", color:"#3a4a6a", borderLeft:"2px solid #e4ede4" }} colSpan={2}>{areaCoverageLabel("SCO", scoPct)}</th>}
              {gridEco && <th style={{ ...thG, textAlign:"center", color:"#3a4a6a", borderLeft:"2px solid #e4ede4" }} colSpan={2}>{areaCoverageLabel("ECO", ecoPct)}</th>}
              <th style={{ ...thG, textAlign:"center", color:"#1f5c2e", borderLeft:"2px solid #e4ede4" }} colSpan={2}>Total</th>
            </tr>
            <tr>
              <th style={{ ...thG, textAlign:"left" }}>Level</th>
              {["MPCI"].concat(gridSco?["SCO"]:[]).concat(gridEco?["ECO"]:[]).concat(["Total"]).map((g,i) => (
                <React.Fragment key={g}>
                  <th style={{ ...thG, borderLeft:"2px solid #e4ede4", fontSize:9.5 }}>Prem</th>
                  <th style={{ ...thG, fontSize:9.5 }}>Cov</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map(cl => { const p = Math.round(cl * 100);
              const br = rowFor ? rowFor(cl) : null;
              const m = br ? (br.ok ? br.q : null)
                : ((opts && opts.quoteRP) || quoteWheatRP2027)({ state, county, aph:A, cl, acres:AC, unit:gUnit, price:+price, vol:+vol, bfr:gridBfr, practice:prac, rateYield:gRateY, adjustedYield:gAdjY });
              const sc = gridSco ? (br ? (br.ok ? br.sco : null) : area(cl, "SCO")) : null;
              const ec = gridEco ? (br ? (br.ok ? br.eco : null) : area(cl, "ECO")) : null;
              const totP = !gridPrem ? 0 : (m?m.netAcre:0) + (sc?sc.netAcre:0) + (ec?ec.netAcre:0);
              const totC = (m?m.liabPerAc:0) + (sc?sc.covPerAc:0) + (ec?ec.covPerAc:0);
              const isCur = cur && cur.cl === p && cur.unit === gUnit;
              const pair = (prem, cov, bold) => (<>
                <td style={{ ...tdG, borderLeft:"2px solid #e4ede4", fontWeight: bold?800:500, color: bold?"#1f5c2e":"#2a3a2a" }}>{num(prem)}</td>
                <td style={{ ...tdG, color:"#5c6f5c" }}>{num(cov)}</td>
              </>);
              return (
                <tr key={cl} style={{ background: isCur ? "#eef6ee" : undefined }}>
                  <td style={{ ...tdG, textAlign:"left", fontWeight: isCur ? 800 : 600, color: isCur ? "#1f5c2e" : "#1a2e1a" }}>
                    {/* ⚠ THE TICK MUST SHOW WHAT THE CHART DRAWS, NOT WHAT WAS PINNED BY HAND.
                        `pinned` was the manual additions alone, so a level the automatic set had
                        chosen drew a bar with no ✓ beside it here — two controls for one thing,
                        disagreeing. `chartLevels` is the selection itself. */}
                    {opts.onPin ? (() => {
                      const onChart = (opts.chartLevels || opts.pinned || []).some(x => Math.abs(x - cl) < 1e-9);
                      return (
                      <button onClick={() => (opts.onChartLevel || opts.onPin)(cl)}
                        title={onChart ? "Remove this stack — it comes off the printed quote too" : "Add this stack — it prints too"}
                        style={{ font:"inherit", color:"inherit", fontWeight:"inherit", background: onChart ? "#e7f3e7" : "transparent",
                                 border:"1px solid " + (onChart ? "#7fb37f" : "transparent"),
                                 borderRadius:5, padding:"1px 6px", cursor:"pointer" }}>
                        {p}%{onChart ? " ✓" : ""}
                      </button>);
                    })() : `${p}%`}
                  </td>
                  {pair(gridPrem&&m?m.netAcre:null, m?m.liabPerAc:null)}
                  {gridSco && pair(gridPrem&&sc?sc.netAcre:null, sc?sc.covPerAc:null)}
                  {gridEco && pair(gridPrem&&ec?ec.netAcre:null, ec?ec.covPerAc:null)}
                  {pair(totP||null, totC||null, true)}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {!gridPrem && <div style={{ fontSize:11, color:"#6b5320", background:"#fdf7e8", border:"1px solid #e8d5a8", borderRadius:7, padding:"6px 9px", marginTop:6 }}><b>Premium columns are blank because no acreage is reported for this unit.</b> Coverage is exact &mdash; it does not depend on acreage. Enter planned acres above to price every level.</div>}
        <div style={{ fontSize:10.5, color:"#677167", marginTop:6 }}>{unitLbl[gUnit]} unit · MPCI {opts.planLabel || "RP"} · <b>Prem</b> = net (producer) $/ac, <b>Cov</b> = coverage $/ac. SCO covers your level→{Math.round(scoEcoBands(MPCI_WHEAT_YEAR).scoTop*100)}%, ECO {Math.round(scoEcoBands(MPCI_WHEAT_YEAR).ecoBottom*100)}→{Math.round(scoEcoBands(MPCI_WHEAT_YEAR).ecoTop*100)}% (both 80%-subsidized). Total = MPCI{gridSco?" + SCO":""}{gridEco?" + ECO":""}.{cur ? " Shaded = current coverage." : ""}</div>
      </div>
    );
  }

  // ── recommendation: best coverage for value (risk management), grounded in 2027 subsidy economics ──
  // Coverage optimizer (v2.1, 2026-07-08 — cost-sensitive). SCO fills level→90, so the MPCI level only chooses
  // whether that band sits on the county trigger (SCO, 80%-subsidized) or the farm's OWN trigger (MPCI). The
  // deciding economics is the MPCI subsidy CLIFF: EU subsidy is 71% at 80% but only 56% at 85%, vs 80% at 75% (BU even
  // lower), so the top 5 individual points are the most expensive coverage in the whole stack — while SCO/ECO
  // sit right there at 80% subsidy covering the same band. So 80% is the natural line: below it, own-trigger
  // coverage is cheap (anchor there); above it, hand the top to SCO/ECO rather than overpay. Kevin rarely
  // writes 85% for exactly this reason.
  //  • MPCI level: ANCHOR 80%. Step UP to 85% ONLY for a farm that both loses often AND independently of the
  //    county (corr < 0.30 AND (below-80% in ≥ 30% of years OR CV ≥ 30%)) — SCO genuinely can't cover it, so
  //    the pricey top points are earned. Step DOWN to 75% when the county clearly carries the 75→80 band:
  //    corr ≥ 0.60 (well above the ~0.52 book median → SCO reliably tracks their losses; ~40% of the book).
  //    Both gated on ≥ 5 overlapping years so we don't act on a noisy correlation. Else 80%. NOT a 50/50 quota
  //    (Kevin 2026-07-08 — recommend best-for-cost, don't force an even split).
  //  • SCO + ECO: default on (80%-subsidized, ~10:1+ coverage/premium). Correlation sets the effectiveness
  //    NOTE, not the call. The marginal $cov/$prem ratios are still computed and shown as the cost lens.
  // series = the unit's [{yr,y}] yield history; unitSt = 2-letter state (for the area-correlation reference).
  // Both default from the current farm so existing callers keep working; batch/print pass another farm's data.
  // rateYield/adjustedYield are OPTIONAL and trail the signature so existing callers are
  // unaffected — but pass them wherever the policy is known. The recommendation is chosen by
  // COMPARING premiums across levels, so computing them un-adjusted can pick a different
  // level than the grower would actually be charged for. See the yield note in wheatQuotePages.
  // THIN ADAPTER over src/wheat/coverageRecommend.js. The decision lives in the
  // module so the checker drives the SHIPPED rule; this maps it onto the shape
  // the five existing call sites and the PDF already read, and carries the new
  // evidence alongside. Do not re-derive anything here.
  function recommendWheat(state, county, aph, acres, price, vol, cv, series, unitSt, rateYield = null, adjustedYield = null, practice = "003", extra = null) {
    // ⚠ NO `|| 1`. Acres are a RATING input and this adapter used to invent one,
    // which invents a UNIT STRUCTURE with it — measured on NY LIVINGSTON, the
    // BU->EU flip at 20 acres moves the 75->80 cost 27% while the whole discount
    // band from 100 to 1,000 acres moves it 3%. `acreage` carries provenance and
    // the engine withholds the premium half when nothing may price it.
    const A = +aph, AC = +acres || 0; county = (county || "").toUpperCase();
    if (!wheatCountyInSet(state, county, practice) || !A || !(+price > 0)) return null;
    const uSt = unitSt || FIPS_STATE[state] || "NY";
    const ser = series || yieldSeries(crop, county);
    const rec = recommendWheatCoverage({
      state, county, aph: A, price: +price, vol: +vol, practice,
      acreage: (extra && extra.acreage) || {
        reportedAcres: AC || null,
        plannedAcres: (extra && extra.plannedAcres) || null,
        priorYearAcres: (extra && extra.priorYearAcres) || null,
        // The farm's own APH-database acreage — what lets the coverage comparison run
        // before this year's acres exist. Never prices a printed premium; the basis is
        // flagged `estimated` and `premiumWithheld` stays true on it.
        aphHistoryAcres: (extra && extra.aphHistoryAcres) || null,
        aphHistoryYear: (extra && extra.aphHistoryYear) || null,
      },
      rateYield, adjustedYield,
      farmSeries: ser, areaRows, areaState: uSt,
      // The unit's own quote — per database where it is a blend — so the levels the
      // engine compares are priced exactly as the columns beside it (extra.quoteRP).
      quoteRP: (extra && extra.quoteRP) || quoteWheatRP2027, quoteArea: quoteAreaRP2027,
      ppAcres: extra && extra.ppAcres != null ? extra.ppAcres : null,
      insuredAcres: extra && extra.insuredAcres != null ? extra.insuredAcres : AC,
      unitStructure: extra && extra.unitStructure ? extra.unitStructure : null,
      // The structure the AGENT stated, which outranks the policy's inside the engine
      // (`unit` beats `unitFromPolicy`). Without this line the pick reached neither the
      // engine nor the quote beneath it, and the printed premium was rated at the
      // policy's structure while the card priced the agent's.
      unit: (extra && extra.unit) || null,
      aphProvenance: extra && extra.aphProvenance ? extra.aphProvenance : null,
      farmerAcceptsBasisRisk: !!(extra && extra.acceptsBasisRisk),
      // The agent's own pick, when there is one. See coverageRecommend's `selection`.
      selection: (extra && extra.selection) || null,
      // THE RENEWAL BASELINE. Where the grower already holds an election, that
      // is what a change is measured against — 80% stays the analytical
      // benchmark, not a destination the engine walks to for want of evidence.
      incumbent: (extra && extra.incumbent) || null,
    });
    if (!rec) return null;
    // legacy fields the existing renderers read, derived from the module result
    const ratios = (rec.levels || []).slice(1).map((h, i) => {
      const l = rec.levels[i];
      const dL = (h.stacks.RP_SCO_ECO || h.stacks.RP).individualLiabPerAc - (l.stacks.RP_SCO_ECO || l.stacks.RP).individualLiabPerAc;
      const dP = (h.stacks.RP_SCO_ECO || h.stacks.RP).totalNetAcre - (l.stacks.RP_SCO_ECO || l.stacks.RP).totalNetAcre;
      return { from: l.cl, to: h.cl, ratio: dP > 0 ? dL / dP : Infinity };
    });
    return {
      ...rec,
      // legacy shape
      why: rec.why, netAc: rec.netAcre, ratios,
      upCost: rec.nextLevelUp ? rec.nextLevelUp.netCostPerAc : null,
      agentSelected: rec.classification === "AGENT_SELECTED",
      corr: rec.fit.r, corrN: rec.fit.nOverlap, lossFreq: rec.need.lossFreq80,
      fitState: rec.fit.fit, hitRate: rec.fit.hitRate,
      areaSentence: areaFitSentence(rec.fit, uSt),
      structures: rec.levels.map((l) => ({
        cl: l.cl, mpciSubsidyPct: l.mpciSubsidyPct,
        ...(l.stacks.RP_SCO_ECO || l.stacks.RP_SCO || l.stacks.RP_ECO || l.stacks.RP),
      })),
      bands: [rec.moves.down, rec.moves.up].filter(Boolean),
    };
  }

  // ── Renewal comparison for ONE county ──────────────────────────────────────
  // Three states side by side: what 2026 actually was, what that identical election
  // costs at 2027 rates, and what we recommend. Shared by the screen and the printed
  // quote so the two can never disagree.
  //
  // THE 2026 COLUMN IS NOT RE-QUOTED. It reports the AIP's own liability and producer
  // premium at the APH, level, unit and options that were actually written — Kevin's
  // rule, and the right one: the grower can check it against his own statement, and any
  // gap between it and our engine is a fact about us, not something to paper over by
  // showing our number in both columns.
  //
  // EVERYTHING IS PER ACRE. We quote $/ac, never a total — Kevin, 2026-08-05. What has to
  // be carried over from 2026 is the ELECTION: coverage level, unit structure (i.e. did
  // they qualify for EU), the options string, and whether SCO/ECO were on.
  //
  // Acreage is still an INPUT even so, and that is not a contradiction: the enterprise
  // discount is banded by acres (P11-1 §16), so the per-acre premium itself moves with
  // acreage — Niagara at 85% runs 0.764 between 50 and 100 acres against 0.721 above 800,
  // about 5% of premium. It is a rating input, not a quantity we quote.
  //
  // It defaults to last year's reported acres because fall 2027 reporting is Nov 15 and
  // acres are null on every 2027 row book-wide. `acresFrom` tells the caller which year
  // it came from so the page can say so; acresOverride lets the agent type the real
  // number when the grower knows it.
  // `practice` is an OVERRIDE. Left off, it comes from the coverage itself — this year's row
  // if the AIP has written one, else last year's — because a renewal is quoted on the practice
  // the grower actually farms, and organic is a different rate table AND a different price
  // election (2026 wheat: $11.16 vs $5.76). The passed-in price/vol are the CONVENTIONAL
  // numbers from the price box; pxFor() converts them for the practice, so an organic unit
  // never silently inherits the CBOT tracking price.
  const countyCompare = ({ county, fips, row27, row26, sco26, eco26, price, vol, series, acresOverride, practice }) => {
    const prac = practice || practiceCodeOf(row27 || row26);
    const pxq = pxFor(prac, fips);
    if (prac !== "003") { price = pxq.price; vol = pxq.vol; }
    const cl26 = row26 && row26.coverage_level != null ? +row26.coverage_level : null;
    const unit26 = normUnit(row26 && row26.unit_structure) || "EU";
    const acres = +acresOverride || (row26 && +row26.acres) || 0;
    const acresFrom = +acresOverride ? "entered" : (row26 && +row26.acres ? 2026 : null);
    // This year's yields: prefer the 2027 record, fall back to last year's if the AIP
    // has not restated it yet. Never silently mix the two.
    const src = row27 || row26 || null;
    const aph = src && src.aph_yield != null ? +src.aph_yield : null;
    const rateY = src && src.rate_yield != null && +src.rate_yield > 0 ? +src.rate_yield : null;
    const adjY = src && src.adjusted_yield != null && +src.adjusted_yield > 0 ? +src.adjusted_yield : null;
    const aphFrom = row27 && row27.aph_yield != null ? 2027 : (row26 ? 2026 : null);
    if (!aph || !acres || !wheatCountyInSet(fips, county, prac) || !(+price > 0)) return null;

    const perAc = (v) => (v == null || !acres ? null : +v / acres);
    const last = {
      cl: cl26, unit: unit26, aph: row26 && row26.aph_yield != null ? +row26.aph_yield : null,
      opts: String((row26 && row26.insurance_options) || "").toUpperCase(),
      acres: row26 ? +row26.acres || null : null,
      mpci: row26 ? { cov: perAc(row26.liability), prem: perAc(row26.net_premium) } : null,
      sco:  sco26 ? { cov: perAc(sco26.liability), prem: perAc(sco26.net_premium) } : null,
      eco:  eco26 ? { cov: perAc(eco26.liability), prem: perAc(eco26.net_premium) } : null,
    };
    const q = (cl, unit, withSco, withEco) => {
      const m = quoteWheatRP2027({ state: fips, county, aph, rateYield: rateY, adjustedYield: adjY, cl, acres, unit, price, vol, practice:prac,
        yieldOptions: src ? src.yield_options ?? null : null, electedOptions: src ? src.insurance_options ?? null : null });
      if (!m) return null;
      const s = withSco ? quoteAreaRP2027({ state: fips, county, endorsement: "SCO", baseCl: cl, aph, acres, price, vol, practice:prac, coveragePct: areaCoverageFractionOfPct(sco26 ? endorsementCoveragePct(sco26) : AREA_COVERAGE_PCT_DEFAULT) }) : null;
      const e = withEco ? quoteAreaRP2027({ state: fips, county, endorsement: "ECO", baseCl: cl, aph, acres, price, vol, practice:prac, coveragePct: areaCoverageFractionOfPct(eco26 ? endorsementCoveragePct(eco26) : AREA_COVERAGE_PCT_DEFAULT) }) : null;
      const prem = (m.netAcre || 0) + (s ? s.netAcre : 0) + (e ? e.netAcre : 0);
      const cov = (m.liabPerAc || 0) + (s ? s.covPerAc : 0) + (e ? e.covPerAc : 0);
      return { cl, unit, m, s, e, prem, cov, capExceeded: !!m.capExceeded };
    };
    // "Same again" mirrors 2026's election exactly, including whether they carried
    // SCO/ECO — quoting them into endorsements they never had is not "changing nothing".
    const same = cl26 != null ? q(cl26, unit26, !!sco26, !!eco26) : null;
    const rec = recommendWheat(fips, county, aph, acres, price, vol, cvOf(series), series, FIPS_STATE[fips], rateY, adjY, prac,
      { incumbent: cl26 != null ? { level: cl26, plan: (row26 && row26.plan) || null,
          mpciPlanCode: (row26 && row26.mpci_plan_code) || null,
          priceElectionPct: (row26 && row26.price_election_pct) != null ? +row26.price_election_pct : null } : null });
    const recQ = rec ? q(rec.cl, rec.unit, !!rec.sco, !!rec.eco) : null;
    // Bars carry the RECOMMENDED endorsement election at every level, so the comparison
    // isolates the one variable being decided: the MPCI level.
    const bars = recQ
      ? wheatBarLevels(rec.cl, cl26).map(cl => {
          const b = q(cl, rec.unit, !!rec.sco, !!rec.eco);
          return b && { ...b, isRec: Math.abs(cl - rec.cl) < 1e-9, isLast: cl26 != null && Math.abs(cl - cl26) < 1e-9 };
        }).filter(Boolean)
      : [];
    // Coverage bought per premium dollar, per layer. Deliberately NOT summed into one
    // headline: the layers trigger on different events (MPCI on this farm's yield,
    // SCO/ECO on the county), so a blended ratio would read as comparable when it isn't.
    const ratio = (o) => (o && o.prem > 0 && o.cov > 0 ? o.cov / o.prem : null);
    const ratios = recQ ? {
      mpci: ratio(recQ.m ? { cov: recQ.m.liabPerAc, prem: recQ.m.netAcre } : null),
      sco:  ratio(recQ.s ? { cov: recQ.s.covPerAc, prem: recQ.s.netAcre } : null),
      eco:  ratio(recQ.e ? { cov: recQ.e.covPerAc, prem: recQ.e.netAcre } : null),
    } : null;
    return { county, fips, acres, acresFrom, aph, aphFrom, rateY, adjY, last, same, rec, recQ, bars, ratios,
             practice:prac, practiceInferred: !practice && practiceInferred(row27 || row26) && prac !== "003",
             price, vol, priceStatus:pxq.status, priceNote:pxq.note || null };
  };

  // Pure page-builder — renders one county page per quotable unit (insured + operated) for ANY farm's data.
  // Used by both the single-farm and batch printers so the optimizer/quote logic lives in exactly one place.
  // arcByCounty (optional, keyed "ST|COUNTY") lets the Full Strategy Report show each county's
  // ARC/PLC $/base-ac as a companion beside the coverage stack. The plain quote print omits it.
  // ONE UNIT PER (COUNTY x PRACTICE), current crop year winning and the prior year riding
  // along as _prev — the same collapse the screen does. Printing the raw rows gave a page
  // per crop-year row, so a farm carried on both years printed each county twice with two
  // different APHs.
  //
  // Extracted from wheatQuotePages so the trigger-record page walks the SAME units the
  // quote pages do. Two copies of this collapse would drift the moment one grew a rule —
  // the pattern this file has been bitten by repeatedly (matchSupplementals, plantedSplit).
  // `pYears` is NOT optional: without it every row falls to the `!g.cur` branch and
  // whichever crop year arrived first silently decides the APH. check-shared-rules holds
  // the arity.
  //
  // ⚠ `aphCtx` IS NOT OPTIONAL EITHER, and it is the reason this function exists in its
  // present form. `{ units, agent }` — `units` is THIS farm's crop_coverage_units rows,
  // `agent` the on-screen APH edit + database ticks, or null where the caller cannot vouch
  // they belong to this farm (the batch printer). Every unit it returns carries `_aph`, the
  // SAME resolution the card prices on (resolveUnitAph). Until 2026-09-18 the printed pages
  // read `r.aph_yield` — the CY2027 summary row, an UNWEIGHTED average that on Clark Five's
  // Huntingdon wheat was the four databases he did not plant (75.0) — while the card beside
  // it priced the ground he farms (98.7). check-shared-rules holds this arity at 5.
  const wheatPrintUnits = (cov, phRows, pStates, pYears, aphCtx) => {
    const uSt = r => (pStates[String(r && r.policy_id)] || "NY").toUpperCase();
    const rawBase = (cov || []).filter(c => c.crop === "WHEAT" && !isEndorsement(c.plan) && c.aph_yield != null);
    const pYr = rr => { const y = pYears ? pYears[String(rr && rr.policy_id)] : null; return y == null ? null : +y; };
    const grp = {};
    for (const rr of rawBase) {
      const c = (rr.county || "").toUpperCase(); if (!c) continue;
      const pc = practiceCodeOf(rr), k = wheatUnitKey(rr);   // one key definition, not a third copy
      const g = (grp[k] ||= { cur:null, prev:null, county:c, prac:pc });
      const y = pYr(rr);
      if (y === MPCI_WHEAT_YEAR) { if (!g.cur) g.cur = rr; }
      else if (y === MPCI_WHEAT_YEAR - 1) { if (!g.prev) g.prev = rr; }
      else if (!g.cur) g.cur = rr;
    }
    // ⚠ WHICH YEAR THIS ROW IS. `g.cur || g.prev` falls back to last year's coverage
    // when the AIP has not written this year's, and the spread erased the distinction.
    // 85 of 214 wheat cells are on that fallback today.
    const base = Object.values(grp).map(g => ({ ...(g.cur || g.prev), county: g.county, practice_code: g.prac, _prev: g.prev || null,
      _aphYear: g.cur ? MPCI_WHEAT_YEAR : (g.prev ? MPCI_WHEAT_YEAR - 1 : null),
      _curAcres: (g.cur && +g.cur.acres > 0) ? +g.cur.acres : null }));
    const covered = new Set(base.map(r => (r.county || "").toUpperCase()));
    const wheatPid = base[0] ? base[0].policy_id : null;
    const extra = [...new Set((phRows || []).filter(p => p.crop === "WHEAT" && p.county).map(p => (p.county || "").toUpperCase()))]
      .filter(c => !covered.has(c)).map(c => {
        const s = yieldSeriesOf(phRows, "WHEAT", c); if (!s.length) return null;
        const calc = computeApprovedYield2027({ state: stFips(uSt({ policy_id: wheatPid })), county: c, yields: s.map(o => ({ year:o.yr, yield:o.y })) });
        if (!calc || calc.approved == null) return null;
        const ac = (phRows || []).filter(p => p.crop === "WHEAT" && (p.county || "").toUpperCase() === c && p.acres != null && +p.acres > 0).sort((a,b)=>b.crop_year-a.crop_year);
        return { county:c, aph_yield:calc.approved, acres: ac.length?+ac[0].acres:null, coverage_level:0.75, unit_structure:"EU", policy_id:wheatPid, _quoteOnly:true, _curAcres:null };
      }).filter(Boolean);
    // Biggest county first, deterministically. Object.values() ordering put Meisenzahl's
    // dormant 46-acre Ontario ahead of his 185-acre Monroe.
    // THE APH EACH UNIT PRINTS ON — the card's resolver, fed THIS farm's rows. Keys match the
    // card exactly: the edit is keyed by wheatUnitKey (county + the coverage's own practice),
    // the database ticks by county + the practice the agent may have overridden to.
    const agent = aphCtx && aphCtx.agent ? aphCtx.agent : null;
    const withAph = r => {
      const prac = pracOv[wheatOvrKey(r)] || practiceCodeOf(r);
      const county = (r.county || "").toUpperCase();
      return { ...r, _aph: resolveUnitAph(r, { units: (aphCtx && aphCtx.units) || [], years: pYears, phRows,
        fips: stFips(uSt(r)), prac, fo: farmOpts[wheatOvrKey(r)] || FARM_OPTS_OFF,
        edit: agent && agent.edits ? agent.edits[wheatUnitKey(r)] : null,
        sel: agent && agent.sels ? agent.sels[county + "|" + prac] : null,
        planned: agent && agent.unitAc ? agent.unitAc[county + "|" + prac] : null }) };
    };
    return [...base, ...extra].sort((a, b) => {
      const ac = x => +x.acres || +((x._prev && x._prev.acres) || 0) || 0;
      return (ac(b) - ac(a)) || String(a.county || "").localeCompare(String(b.county || ""));
    }).map(withAph);
  };

  // `levelSink` — an optional { COUNTY: level } object this fills in with the level it
  // RECOMMENDED for each county, so the trigger record beside it can be measured at the
  // same level rather than re-deriving a recommendation of its own.
  // `recSink` — the same idea for the WHOLE recommendation object, and it exists
  // because the level alone was not enough. The strategy page needs the premium,
  // the endorsement election, the moves and the reasons, so it made its OWN
  // recommendWheat call — with different arguments (no `incumbent`, no `cv`, and
  // CY2027 acres where this one uses the resolved acreage). One document then
  // recommended TWO coverage levels: page 1 printed 80% and page 2 printed 85%
  // for KAST FARMS on 2026-09-05. Same shared rule, two call sites, two answers —
  // the class check-shared-rules exists for, on the one function this repo has
  // already recorded it on once (the card called recommendWheat SHORT).
  // The quote page is the authority because it is the page that prices the quote.
  // `recOverrides` — the agent's on-screen edits, keyed exactly as the card keys them
  // (wheatUnitKey: COUNTY|PRACTICE, via the SAME function, never a second copy). Without
  // it the sheet printed the engine's answer while the screen showed the agent's, which
  // is the one disagreement a document handed to a grower must not carry.
  //
  // ⚠ IT IS PASSED ONLY BY THE SINGLE-FARM PRINT. wheatUnitKey is farm-agnostic by
  // design, so two growers in LANCASTER|003 share a key — the same hazard wheatPlannedKey
  // exists to solve. A batch print walks many farms and must not apply one farm's edits
  // to another's sheet, so it passes nothing and prints the engine's answer.
  // `printCfg` — which SECTIONS the document carries and which coverage stacks each unit
  // draws. An options object rather than a fifteenth positional argument: this signature is
  // already long, and a rule called SHORT is the failure this file keeps recording. Every
  // section defaults ON via wheatPrintCfg, so a caller that passes nothing (the batch
  // printer) prints exactly what it printed before.
  const wheatQuotePages = (farmName, cov, phRows, pStates, pNumbers, basePrice, baseVol, logoSrc, arcByCounty, pYears, onlyCounty, levelSink, recOverrides, recSink, printCfg) => {
    const PC = wheatPrintCfg(printCfg);
    // ⚠ DERIVED FROM **THIS** FARM'S ROWS, NOT FROM COMPONENT STATE. The batch printer
    // walks many growers through this one function, so a BFR status read off the screen
    // would print one farm's subsidy on another's quote — the same hazard wheatUnitKey
    // already carries here. See src/bfrStatus.js: it is a DERIVATION off the subsidy the
    // AIP stamped, never a field M13 states.
    const bfrByPolicy = BFR.deriveBfr(cov, { bookRows: cov, cropYear: MPCI_WHEAT_YEAR - 1 });
    // Per POLICY, not per county — see BFR.bfrByStem. This printer walks many farms, so
    // the number map is the pNumbers PARAMETER, never component state.
    const bfrStemIdx = BFR.bfrByStem(bfrByPolicy, (pid) => (pNumbers || {})[String(pid)]);
    const bfrOptOf = (row, fallbackRow) => {
      // ⚠ THE AGENT'S OVERRIDE ARRIVES AS A PARAMETER, NEVER OFF THE SCREEN. The
      // reasoning above still holds — this function prints many farms and must not read
      // component state — but "cannot read state" is not "must ignore the agent". The
      // caller that HAS an override passes it in `printCfg`; the batch caller passes
      // none and keeps deriving per farm, so it cannot inherit one grower's tick.
      // Same rule the screen uses, so the two cannot drift: BFR.bfrModeOption.
      const ov = BFR.bfrModeOption(PC.bfrMode);
      if (ov) return ov;
      const st = BFR.bfrStatusFor(bfrByPolicy, bfrStemIdx, row, fallbackRow, (pid) => (pNumbers || {})[String(pid)]);
      // The observation is LAST year's policy and the quote is NEXT year's, so the
      // durable +10 carries and the first-two-years +5 does NOT — it expires, and the
      // record cannot say whether the quote year is still inside it.
      const seed = BFR.bfrQuoteSeed(st, { observedYear: MPCI_WHEAT_YEAR - 1, quoteYear: MPCI_WHEAT_YEAR });
      return { bfr: seed.bfr, bfrFirstTwoYears: seed.firstTwoYears, bfrBenefitYear: seed.benefitYear,
               bfrSteppedDown: seed.steppedDown, bfrQuotedTier: seed.quotedTier, bfrObservedTier: seed.observedTier,
             // How the status was established, carried so the printed sheet can say WHERE
             // it came from rather than only that it was applied. STATED_* is the AIP's
             // own waiver code; anything else is this engine's inference off the subsidy.
             bfrMethod: st.method || null };
    };
    const uSt = r => (pStates[String(r && r.policy_id)] || "NY").toUpperCase();
    const endos = (cov || []).filter(c => c.crop === "WHEAT" && isEndorsement(c.plan));
    const units = wheatPrintUnits(cov, phRows, pStates, pYears, PC.aph)
      .filter(u => !onlyCounty || (u.county || "").toUpperCase() === String(onlyCounty).toUpperCase());
    const uLbl = { OU:"Optional", BU:"Basic", EU:"Enterprise" };
    const per = (v, ac) => v != null && ac ? v / ac : null;
    const d0 = n => n == null ? "—" : "$" + Math.round(n).toLocaleString();
    const d2 = n => n == null ? "—" : "$" + (Math.round(n*100)/100).toFixed(2);
    const esc = s => String(s == null ? "" : s).replace(/[&<>]/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;" }[c]));
    return units.map(r => {
      // THE APH IS THE CARD'S, resolved once per unit in wheatPrintUnits — never
      // `+r.aph_yield`, which on a CY2027 row is an unweighted summary (see resolveUnitAph).
      const RA = r._aph;
      const county = (r.county || "").toUpperCase(), fips = stFips(uSt(r)), aph = +RA.aph;
      // Acres: reported, else last year's reported, else the most recent PLANTED acres from
      // production history. An elected-but-unreported county (Meisenzahl's Ontario carries
      // null acres in BOTH crop years) has nothing to quote against otherwise, and the old
      // "|| 0" guard dropped it from the printed quote entirely while the screen showed it.
      const histRef = wheatHistAcres(phRows, county);
      const histAc = histRef.acres;
      // …and finally the PLANNED acres the agent typed on the card. Without this the screen
      // quotes a county the printed document silently omits — the same divergence the note
      // above records being fixed once already, arriving by a new route.
      // Acres typed per APH database outrank a county total: they ARE the planting estimate.
      const plannedPrintAc = RA.plannedUnitAcres > 0 ? RA.plannedUnitAcres
        : (+(plannedAc[wheatPlannedKey(r)] || 0) > 0 ? +plannedAc[wheatPlannedKey(r)] : 0);
      // THE QUOTED YEAR, OR A FIGURE A HUMAN TYPED. Nothing else prices a premium.
      // This was `+r.acres || prior-year || histAc || planned` — a four-deep ladder whose
      // first rung was already the prior-year row on any unit with no current coverage, so
      // the `if (!acres)` guard below it almost never fired and the printed document -
      // the one the grower actually holds - quoted MPCI, SCO and ECO against acreage
      // nobody had reported for the year on its own heading.
      const refAcres = +((r._prev && r._prev.acres) || 0) || histAc || 0;
      // ── THE QUOTE BASIS — ONE LADDER, EVERY RUNG NAMED ───────────────────────
      // Kevin, 2026-09-02: "dont require acres to print quote, If i want to print a
      // quote quick I should be able to. Default to last years or 100 acres."
      //
      // ⚠ THE OLD LADDER RESOLVED TO NOTHING ON EVERY FARM IN THE BOOK. Reported
      // acreage is on ZERO of 334 CY2027 wheat coverages, so `reported || typed` was 0
      // unless somebody had typed a figure — and the `if (!acres)` branch below returned
      // a stripped page with the premium withheld, NO bar comparison, NO recommendation
      // and NO year-over-year page. That branch is gone: this ladder always yields a
      // quotable figure, so the full document prints for every county the grower carries.
      //
      // ⚠ AND THAT ZERO IS NOT A TIMING ACCIDENT — IT IS THE WORKFLOW. Kevin,
      // 2026-09-02: "We will never have 2027 acres reported when quoting someone... we
      // quote them before they plant." A quote is written at or before sales closing;
      // acreage is reported after the crop is in the ground. So the REPORTED rung is
      // structurally empty at quote time and always will be — it exists for the review
      // case, months later, on the same code path. Do not read its emptiness as a gap
      // waiting to be filled, and do not "fix" this by waiting for it.
      //
      // That is why the prior-year rung is the working default rather than a stopgap:
      // it is the grower's own filed figure and measurably closer than the invented 100
      // — Kast at his real 458.8 ac prices $14.63/ac against $14.95 (+2.2%). What makes
      // it usable is that the page NAMES which rung priced it, so a grower who is moving
      // from 458 acres to 700 can see which number the premium rests on. It is a
      // statement of basis, not a warning: it is the normal case, and a page that
      // apologised for it every time would be a page nobody reads.
      const basis = resolveAcreageBasis({
        reportedAcres: wheatQuotedYearAcres(r),
        plannedAcres: plannedPrintAc,
        priorYearAcres: refAcres,
        // The year the reference acres are FROM: the prior-year row's own year, or the
        // production-history row's — never assumed to be last year.
        priorYear: +((r._prev && r._prev.acres) || 0) > 0 ? MPCI_WHEAT_YEAR - 1 : histRef.year,
        assumedAcres: DEFAULT_QUOTE_ACRES,
      });
      const acres = basis.acres;
      // ⚠ AN INVENTED ACREAGE IS NOT THIS FARM'S ACREAGE (2026-09-19). With nothing reported,
      // entered or on file the ladder still hands back its default so the acre-invariant
      // figures (guarantee, coverage) compute — but the page prints "Acres — Pending" and NO
      // premium: the premium moves with acreage through the BU/EU discount band, so a premium
      // on 100 invented acres is not a figure for this customer. Metzler's Harford quote
      // printed "$17.29 you pay /ac" beside "premium not quoted".
      const acresPending = basis.source === ACREAGE_SOURCE.ASSUMED;
      const acresEst = basis.source !== ACREAGE_SOURCE.REPORTED;
      // What the meta line prints beside the acreage. REPORTED adds nothing; every
      // other rung says what it is, on the page the grower actually holds.
      const basisNote = basis.source === ACREAGE_SOURCE.REPORTED ? ""
        : basis.source === ACREAGE_SOURCE.PLANNED ? " you entered"
        : basis.source === ACREAGE_SOURCE.PRIOR_YEAR ? ` \u2014 your ${basis.reference.priorYearOf || "earlier"} acres`
        : " assumed \u2014 none on file";
      // \u26a0 AND THE SAME FACT, SPELLED OUT, WHEREVER A FOOTER NAMES THE BASIS. This used to
      // be `acresEst ? " against planned acres" : ""`, which called a prior-year basis
      // "planned" \u2014 a wrong label for the acreage on a document a grower signs, and wrong
      // on essentially every quote, since PLANNED is one rung and the common case is the
      // one below it.
      const basisPhrase = basis.source === ACREAGE_SOURCE.REPORTED ? `${MPCI_WHEAT_YEAR} reported acres`
        : basis.source === ACREAGE_SOURCE.PLANNED ? "the acres entered on the quoter"
        : basis.source === ACREAGE_SOURCE.PRIOR_YEAR ? `the grower's ${basis.reference.priorYearOf || "earlier"} acres`
        : "no acreage on file";
      // PRACTICE IS PER UNIT, not per farm — a grower can carry organic ground in one county
      // and conventional in the next, and they are two different rate tables and two
      // different price elections. The price passed in is the conventional one; pxFor()
      // converts it, and a certified-organic unit with no election on file prints nothing
      // rather than a conventionally-priced page under an organic heading.
      // THE PRINTED SHEET HONOURS WHAT THE AGENT SET ON SCREEN (BUG-014).
      // wheatQuotePages read neither the practice override nor the APH-option
      // ticks, so an agent who corrected a unit to organic, or elected TA, saw it
      // on screen and got a PDF quoting the uncorrected unit — with no indication
      // the two disagreed.
      //
      // ⚠ READING COMPONENT STATE HERE IS ONLY SAFE BECAUSE THE KEY CARRIES THE
      // POLICY. The batch printer walks many growers through this one function, so
      // a farm-agnostic key would print the loaded farm’s overrides on every other
      // grower’s sheet — the same hazard the BFR note above records, and the reason
      // wheatOvrKey exists. `plannedAc` has read from this closure on a policy-
      // scoped key since September; this follows it.
      const oKeyP = wheatOvrKey(r);
      const uPrac = pracOv[oKeyP] || practiceCodeOf(r);
      const foP = farmOpts[oKeyP] || FARM_OPTS_OFF;
      const uPx = pxFor(uPrac, fips);
      const uPpf = wheatPpFactor2027(fips, county, uPrac, !!foP.pf);   // PP factor from the ADM, per county+practice+PF election
      const price = uPrac === "003" ? basePrice : uPx.price, vol = uPrac === "003" ? baseVol : uPx.vol;
      // ⚠ A COUNTY THE QUOTE CANNOT PRICE IS STILL ONE THE GROWER INSURES. The printed
      // quote has always skipped it; the strategy report records it as PENDING instead,
      // so an organic unit awaiting RMA's election reads "not priced yet" rather than
      // vanishing from the page that summarises the whole farm.
      if (PC.factSink && wheatCountyInSet(fips, county, uPrac) && !(+price > 0)) {
        PC.factSink[wheatUnitKey(r)] = { key: wheatUnitKey(r), county, state: FIPS_STATE[fips] || null, practiceCode: uPrac, policyId: r.policy_id || null,
          practiceLabel: uPrac === "003" ? null : (WHEAT_PRACTICE_NAMES[uPrac] || uPrac), cropYear: MPCI_WHEAT_YEAR,
          priorYear: MPCI_WHEAT_YEAR - 1, priceMissing: true, current: null, proposed: null,
          acreage: { acres: basis.acres, source: basis.source, year: basis.source === ACREAGE_SOURCE.PRIOR_YEAR ? basis.reference.priorYearOf : null } };
      }
      if (!wheatCountyInSet(fips, county, uPrac) || !(+price > 0)) return "";
      // The RATE and ADJUSTED yields, not just the approved one. Both are load-bearing and
      // BOTH must be passed or the quote silently comes out light on any trend-adjusted
      // policy: rateYield drives the base rate (§3) and adjustedYield drives the effective
      // coverage level (§13). Omitting adjustedYield defaults it to the approved yield, so
      // eff == cl and the trend-adjustment rating never happens at all.
      //
      // Verified against R&H's Coverage Analyzer for ATWATER FARMS (MP-865508, NY Niagara,
      // EU 50%, approved 92 / rate 87 / adjusted 87, 524 ac, $6.95 @ 0.23 vol):
      //   approved only            -> $4.18/ac   (8% light)
      //   + rateYield              -> $4.33/ac   (still 4.5% light)
      //   + rateYield & adjusted   -> $4.54/ac   == R&H exactly, $2,377 producer premium
      // 54 of 112 CY2027 wheat coverages carry a real trend adjustment, so this was the
      // majority-case number on a document that goes to the grower.
      //
      // ⚠ ALL THREE FROM `r._aph` — the card's resolver — never `r.rate_yield` /
      // `r.adjusted_yield`. On a CY2027 row those are the summary's UNWEIGHTED averages
      // over whatever databases the AIP has re-approved, and they follow the APPROVED
      // yield's selection or the effective coverage level (approved / adjusted) is a
      // trend adjustment nobody has. Clark Five printed 75.0 / 70.8 / 73.5 against the
      // 98.7 / 92.4 / 92.4 of the ground he farms.
      const rateY = RA.rateY != null && +RA.rateY > 0 ? +RA.rateY : null;
      const adjY  = RA.adjY != null && +RA.adjY > 0 ? +RA.adjY : null;
      // Elections the AIP recorded on THIS coverage, shown on the quote. NONE of them change
      // the premium: QL and MC both carry ADM option rate 1.0000, and MC was verified against
      // R&H at two Orleans acreages — each county is still rated on its OWN acres. BFR is a
      // per-GROWER status the AIP does not put in this field, so it can only come from the
      // quoter, never from here.
      const optsOf = (v) => new Set(String(v || "").toUpperCase().split(/[^A-Z]+/).filter(Boolean));
      const rOpts = optsOf(r.insurance_options);
      // THE PRIOR-YEAR COLUMN IS THE PRIOR-YEAR ROW. `r` is THIS year's coverage the moment
      // the AIP writes one — or the moment the agent restates the APH on it — and that row
      // carries no acres, no liability and no premium until it bills. Reading the "2026
      // Current" column off `r` therefore printed an em-dash on every figure the grower is
      // supposed to check against his own statement, and it happened the day the renewal
      // work started, not before. Kennedy Farms: MPCI blank on a page that still showed his
      // ECO, because the endorsements below already pick by crop year and the base row did
      // not. Same read also stamped the NEW APH on last year's guarantee (67.4 -> 50.6 bu
      // where 2026 was 58.0 -> 43.5) and called a BASIC unit Enterprise, since the unwritten
      // 2027 row leaves unit_structure null and normUnit() defaults to EU.
      // countyCompare() has always built `last` from row26 alone — this is the print agreeing.
      const pRow = r._prev || null;
      const bfrQ = bfrOptOf(pRow, r);
      const pAcres = (pRow && +pRow.acres > 0) ? +pRow.acres : (acresPending ? null : acres);
      const curCl = (pRow && pRow.coverage_level != null) ? +pRow.coverage_level
                  : (r.coverage_level != null ? +r.coverage_level : 0.75);
      // The SOURCED unit structure — see wheatUnitSource. Where it is unknown the page still
      // prices on normUnit's enterprise-unit fallback and SAYS SO; the strategy report withholds
      // the premium instead.
      const unitSrc = wheatUnitSource(r, pRow, PC.aipAcreage, pYears, pNumbers, MPCI_WHEAT_YEAR);
      // The structure the agent stated for this unit, where the file states none. Carried on
      // the print config (farm-scoped key), exactly like the typed acreage.
      // The agent's stated basis wins; absent one the quote is Enterprise. NOT gated on
      // unitSrc any more — the file's structure is what they are ON, not what we quote.
      const unitEntered = (PC.unitEntries && PC.unitEntries[wheatPlannedKey(r)]) || null;
      const curUnit = wheatQuoteUnit(unitEntered);
      const curAph = (pRow && pRow.aph_yield != null && +pRow.aph_yield > 0) ? +pRow.aph_yield : aph;
      // ⚠ THE PRIOR-YEAR COLUMN NEEDS THE PRIOR YEAR'S PP FACTOR. It was being
      // multiplied by the 2027 ADM factor above — 2026 coverage priced on 2027's
      // product, which is the band defect of 2026-09-02 one table row further
      // down. getPPFactor's own table is headed '2026 Prevent Plant Coverage
      // Factors', so it is the sourced answer for that column. Both are 0.60 for
      // NY wheat today, so no printed figure moves; what changes is that the two
      // columns stop sharing one year's parameter.
      // ⚠ AND THE BUY-UP IS PART OF THE PRIOR YEAR'S FACTOR. `getPPFactor` is the BASE
      // table only, so a grower who elected `PF` read five points light on the one column
      // that exists to be checked against his own statement. RMA's 2026 ADM states it per
      // county: base 0.600, option PF 0.650 — see src/wheatPpPrior.js. THE ELECTION IS
      // READ OFF THE PRIOR ROW, never off `r`: `r` is the quote year, whose own options
      // are a different year's election, and RMA does not offer PF for 2027 at all.
      const prevPp = wheatPpFactorPrior(fips, county, uPrac, pRow && pRow.insurance_options);
      const prevPpf = prevPp.factor != null ? prevPp.factor : getPPFactor('WHEAT');
      // The label carries ONE percentage only while the two years agree. If they
      // ever diverge, a single '(60% of guarantee)' heading over both columns
      // would be a false statement about one of them.
      const ppLbl = (uPpf.base != null && Math.abs(prevPpf - uPpf.base) > 1e-9)
        ? `Prevent plant (${MPCI_WHEAT_YEAR - 1}: ${Math.round(prevPpf*100)}% &middot; ${MPCI_WHEAT_YEAR}: ${Math.round(uPpf.base*100)}% of guarantee)`
        : `Prevent plant (${Math.round((uPpf.base != null ? uPpf.base : prevPpf)*100)}% of guarantee)`;
      // ── NO REPORTED ACREAGE ANYWHERE ─────────────────────────────────────────────
      // RMA reports none, there is no planted history, and nobody typed a planned figure.
      // The county is still PRINTED, because a coverage estimate that silently omits a
      // county the grower insures reads as "you have nothing there" — and the guarantee is
      // exact regardless: it is acre-INVARIANT (measured across 6 counties x 3 unit
      // structures x 5 levels and both area endorsements — identical at 1 acre and 1,000).
      // Acres enter the rating only through the BU/EU discount band, so the PREMIUM is the
      // one figure withheld, and the page says so rather than printing a 1-acre number.
      // ⚠ THE NO-ACREAGE PAGE IS GONE, DELIBERATELY. It printed a different and
      // poorer document — premium withheld, no bars, no recommendation, no
      // year-over-year page — and with reported acreage on 0 of 334 CY2027 rows it was
      // what EVERY farm in the book got. The ladder above always resolves, so there is
      // nothing left to fall back to; an unreachable second document is how it returns.
      // ENDORSEMENTS MUST MATCH THE CROP YEAR, not just the county. A farm carried on both
      // years has two SCO rows and two ECO rows per county, and the 2027 pair is elected but
      // not yet billed, so every figure on it is null. A bare .find() takes whichever row
      // came back first: when that was 2027, Monroe printed em-dashes for its prior year and
      // its total fell from $17.13 to $10.30, while the $431 SCO and $830 ECO the AIP
      // actually billed sat untouched in the data. The screen has always preferred the
      // matching year; the printed quote never did.
      const uEnd = endos.filter(e => (e.county || "").toUpperCase() === county && practiceCodeOf(e) === uPrac);
      const rowYr = pYears ? pYears[String((r._prev || r).policy_id)] : null;
      const pickEndo = test => uEnd.find(e => test(e.plan) && pYears && pYears[String(e.policy_id)] === rowYr)
                            || uEnd.find(e => test(e.plan) && e.net_premium != null)
                            || uEnd.find(e => test(e.plan));
      const cSco = pickEndo(isScoPlan), cEco = pickEndo(isEcoPlan);
      // ── THE SCO / ECO COVERAGE PERCENTAGE ON PAPER ─────────────────────────────
      // This sheet quoted every SCO and ECO at 100% of the band whatever the grower
      // held, while the card beside it priced the election — so a grower carrying ECO
      // at 50% was handed a printed "2027 Same" with double the ECO he owns, at double
      // the premium. Read through the SAME rule the card uses. A layer he does not carry
      // has no election (RMA's default, 100%); a stored value RMA does not sell is null
      // and prices nothing, exactly as on screen.
      const cScoPct = cSco ? endorsementCoveragePct(cSco) : AREA_COVERAGE_PCT_DEFAULT;
      const cEcoPct = cEco ? endorsementCoveragePct(cEco) : AREA_COVERAGE_PCT_DEFAULT;
      const pctFrac = p => (p == null ? null : p / 100);
      // " · ECO 50%" for each layer the column carries below 100%; nothing at 100%.
      const pctHead = (hasSco, scoP, hasEco, ecoP) => [
        hasSco && scoP != null && scoP < 100 ? areaCoverageLabel("SCO", scoP) : null,
        hasEco && ecoP != null && ecoP < 100 ? areaCoverageLabel("ECO", ecoP) : null,
      ].filter(Boolean).map(t => " &middot; " + t).join("");
      const grossOf = (row) => (row && row.total_premium != null && row.net_premium != null)
        ? per(+row.total_premium - +row.net_premium, acres) : null;
      const cur = { mp:per(pRow&&pRow.net_premium!=null?+pRow.net_premium:null,pAcres), mc:per(pRow?pRow.liability:null,pAcres),
                    sp:per(cSco&&cSco.net_premium!=null?+cSco.net_premium:null,pAcres), sc:per(cSco?cSco.liability:null,pAcres),
                    ep:per(cEco&&cEco.net_premium!=null?+cEco.net_premium:null,pAcres), ec:per(cEco?cEco.liability:null,pAcres) };
      // ⚠ THE PREMIUM IS WITHHELD WHERE THE UNIT STRUCTURE IS NOT ON FILE (BUG-088), and the
      // COVERAGE is not: liability and the bushel guarantee never read the unit structure, so
      // they are exact whatever it is. `curUnit` still carries normUnit's EU fallback, which is
      // why every quote below stays computable — what changes is that no premium built on it is
      // PRINTED. unitPrem() is the one gate, so a cell added later inherits it.
      // KNOWN = the AIP keyed it, or the agent stated it. A stated structure is a fact the
      // agent holds and the file does not; normUnit's silent EU fallback is neither, and it
      // still prices nothing.
      // ⚠ KNOWN MEANS STATED BY THE AGENT, NOT PRESENT IN THE FILE. The quote is priced
      // at the agency basis (Enterprise unless stated), so a structure sitting on the
      // POLICY no longer makes the quote basis known — it is what the farm is on, which
      // is a different question. Leaving unitSrc in here printed "80% Enterprise" with no
      // caveat for FIRE CREEK, whose file says Basic.
      const unitKnown = !!unitEntered;
      // …and where the ACREAGE is invented. One gate for every 2027 premium on the page: the
      // quote table, the Total row, the recommendation panel, the stack, the level bars and the
      // comparison page. It used to reach only the level bars, so a unit with no unit structure
      // printed "premium not quoted" beside a full row of premiums.
      //
      // ⚠ THE PRINTED QUOTE AND THE CARD MUST NOT DISAGREE ABOUT WHETHER A PREMIUM EXISTS
      // (Kevin, 2026-09-23: "its showing premium in the quoter but doesnt show when I print").
      // The card's gate is `acres > 0 && !acreageAssumed` and this one was that AND
      // `unitKnown`, so on a unit with no structure on file the card rated it at
      // `normUnit`'s EU fallback and showed a number while the sheet printed dashes.
      // ZELIFF FARMS LLC is the case: no unit structure on either crop year, and none in
      // the AIP's acreage record either — both CY2026 rows exist and are entirely empty.
      // It is not rare. 80 of 127 CY2027 wheat units are in that state until acreage
      // reporting closes, so for most of the book the agent's own working quote printed
      // no price at all.
      //
      // ⚠ THE SILENT ASSUMPTION IS THE THING TO FIX, NOT THE PRINTING. Withholding was
      // right to refuse an unsourced rating input — measured, the BU->EU flip moves the
      // 75->80 cost 27% — but the card was ALREADY making that assumption and showing the
      // number. So the honest repair is to make the assumption VISIBLE on the document
      // rather than to drop the number: the sheet quotes at enterprise rates, says so, and
      // names the direction of the error. Enterprise is the LOWEST structure RMA sells, so
      // a stated basic or optional unit can only cost MORE — and understating a premium on
      // a sheet a grower budgets from is the dangerous direction, which is exactly why it
      // is printed as a caveat and not as a footnote.
      //
      // ⚠ THE CUSTOMER REPORT STILL REFUSES, and that is deliberate rather than an
      // oversight. Kevin, 2026-09-22: a finished customer strategy report must not contain
      // "Premium not quoted" — it stops and tells the agent what input is required. That
      // gate reads `facts.unit`, which is untouched here and still carries null when
      // nothing states a structure. Two documents, two standards: this is the AGENT's
      // working estimate, which may quote on a stated assumption; that one is the grower's,
      // which may not.
      const unitAssumedForPremium = !unitKnown && !acresPending;
      const premiumOk = !acresPending;
      const unitPrem = (v) => (premiumOk ? v : null);
      const quoteRP = RA.quoteRP;   // per database where the APH is a blend — see resolveUnitAph
      // ⚠ CAT ON PAPER, THE CARD'S OWN TEST (2026-09-18). "2027 Same" mirrors the grower's election,
      // and the card has always quoted a CAT election as CAT — 50% of the yield at 55% of the
      // price, premium 100% subsidised, a flat fee instead. This sheet passed neither, so a CAT
      // grower's printed "2027 Same" was a 50% BUY-UP at the full price with a rated premium he
      // will never be billed (Marquart Bros carries CAT wheat in five counties). The
      // discriminator is the 50/55 pair, exactly as on the card.
      const uPePct = (r.price_election_pct != null && +r.price_election_pct > 0 && +r.price_election_pct <= 1) ? +r.price_election_pct : 1;
      const isCat = Math.abs(uPePct - 0.55) < 1e-6 && Math.abs((r.coverage_level != null ? +r.coverage_level : 0) - 0.50) < 1e-6;
      const q = quoteRP({ ...bfrQ, state:fips, county, aph, rateYield:rateY, adjustedYield:adjY, cl:curCl, acres, unit:curUnit, price, vol, practice:uPrac,
        priceElectionPct: uPePct, cat: isCat, plan: isCat ? "YP" : "RP" });
      // "2027 Same" mirrors their CURRENT election — only include SCO/ECO if they carry it today.
      const qs = cSco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"SCO", baseCl:curCl, aph, acres, price, vol, practice:uPrac, coveragePct: pctFrac(cScoPct) }) : null;
      const qe = cEco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"ECO", baseCl:curCl, aph, acres, price, vol, practice:uPrac, coveragePct: pctFrac(cEcoPct) }) : null;
      const series = yieldSeriesOf(phRows, "WHEAT", county, uPrac);
      // ── YIELD CONTEXT: CV AND BASIS ────────────────────────────────────────
      // Kevin, 2026-09-30: "Can we add CV and Basis to the print quote and full
      // strategy report?" They are on the quoter's county table and were on
      // neither document — and they are the two figures that say whether the
      // RECOMMENDATION on this sheet makes sense: CV is the farm's own yield
      // volatility (higher → own-farm MPCI earns its keep) and basis is
      // approved APH ÷ county expected (near 1.0 → the farm tracks its county,
      // so SCO/ECO area coverage fits; far off → basis risk cuts its value).
      //
      // ⚠ THE STATE COMES FROM THE UNIT'S OWN FIPS, NOT FROM COMPONENT STATE.
      // `unitState` reads `polStates`, and the BATCH printer walks many farms
      // through one wheatQuotePages — so it would resolve another grower's
      // state, and a county name shared across states (FRANKLIN, MONROE, ERIE…)
      // would then look up the WRONG county's expected yield and print it as a
      // fact. That is the "cant say NY on a MD PA grower" defect, and here it
      // REFUSES rather than falling back: no state, no basis.
      const uStPost = FIPS_STATE[fips] || null;
      const ctyExpBu = uStPost ? countyExp("WHEAT", county, uStPost) : null;
      const cvPct = cvOf(series);
      // ⚠ NOT `basis` — that name is already the ACREAGE basis in this scope
      // (ACREAGE_SOURCE.REPORTED/PLANNED/…), and shadowing it here would silently
      // rewrite what the footer says the premium was quoted against.
      const yieldBasis = (aph != null && ctyExpBu) ? aph / ctyExpBu : null;
      // The agent's edit for THIS unit, keyed by the same wheatUnitKey the card uses.
      const ovRec = recOverrides ? recOverrides[wheatUnitKey(r)] : null;
      // ⚠ THE STRUCTURE THE AGENT PICKED IS PART OF THE QUOTE, AND `unit` USED TO BE THE ONE
      // FIELD THIS SHEET DROPPED. The reasoning was that the printed sheet should state the
      // structure the AIP has on the policy rather than let a screen toggle rewrite a figure
      // the grower checks against his own statement. That is right about the CURRENT column
      // and wrong about the QUOTE: `q` ("2027 same") still prices on `curUnit` and is
      // untouched here, while the recommendation column is next year's quote, which is
      // exactly what the agent is choosing. Dropping it made the card lie — it re-prices on
      // the pick and prints "and that is what prints" on screen — and the premium on the
      // document was rated at a structure nobody selected.
      //
      // MARQUART BROS LLC is the case, and the CAT history is why it surfaced there: all ten
      // of his wheat coverages are CAT (0.50 level at a 0.55 price election, plan YP), his
      // CY2027 rows carry NO unit structure, and only WYOMING has one to inherit — `BU`,
      // carried off the CY2026 CAT row. So on that one county `wheatUnitSource` resolves a
      // structure, the "state the unit structure" select is therefore NOT offered, and the
      // Coverage pill row is the only control there is. Switching it to Enterprise re-priced
      // the card and printed Basic. The other four counties were right the whole time,
      // because with nothing to inherit they go through `unitEntered`, which the sheet has
      // always honoured.
      //
      // It is a RATING INPUT the agent states, not a recommendation: `recommendWheatCoverage`
      // takes it as `unit` and prefers it over the policy's, the same precedence `unitEntered`
      // already has. It is NOT admitted to `unitKnown` — the card seeds this key with the
      // engine's OWN unit the moment any control on the unit is touched, so a seeded value is
      // a derivation and not a statement, and letting it clear the missing-structure refusal
      // would print a premium rated on a guess.
      const ovUnit = ovRec && WHEAT_UNIT_CODES.includes(String(ovRec.unit || "").toUpperCase())
        ? String(ovRec.unit).toUpperCase() : null;
      // ⚠ THE INCUMBENT IS THE SOURCED ELECTION, NOT `curCl`. curCl carries a display
      // fallback of 0.75 for a row that states no level, and handing that to the engine
      // as an incumbent is a fabricated election — it would "hold the current level" for
      // a grower who has not made one. The card resolves it the same way and stops at
      // null. Measured 2026-09-04: 0 of 781 wheat coverage rows carry a null
      // coverage_level, so this moves no figure today and closes the one path on which
      // the two surfaces could still have disagreed.
      const incSourced = (pRow && pRow.coverage_level != null) ? +pRow.coverage_level
                       : (r.coverage_level != null ? +r.coverage_level : null);
      const recInc = incSourced != null ? { level: incSourced, plan: (pRow && pRow.plan) || null,
          mpciPlanCode: (pRow && pRow.mpci_plan_code) || null,
          priceElectionPct: (pRow && pRow.price_election_pct) != null ? +pRow.price_election_pct : null } : null;
      // ⚠ SILAS'S OWN ANSWER IS COMPUTED FIRST, ALWAYS, AND AN ENTRY IN THE OVERRIDE MAP IS
      // NOT AN OVERRIDE. The card seeds that key with the recommendation the moment an agent
      // touches any control on the unit, so gating on the key's presence stamped
      // "2027 Selected" over levels Silas had chosen — which misattributes the decision on a
      // document the grower keeps. The engine is only re-run under a selection where the pick
      // genuinely DIFFERS. Same comparison as the card's `recOverridden`; the two must agree,
      // because the badge on screen and the header on the sheet are one claim.
      const recSilas = recommendWheat(fips, county, aph, acres, price, vol, cvOf(series), series, FIPS_STATE[fips], rateY, adjY, uPrac, { incumbent: recInc, unitStructure: unitSrc ? unitSrc.unit : null, unit: curUnit, quoteRP: RA.quoteRP,
        aphHistoryAcres: histRef.acres || null, aphHistoryYear: histRef.year || null });
      const ovDiffers = !!(ovRec && recSilas && (
        (ovRec.cl != null && Math.abs(+ovRec.cl - recSilas.cl) > 1e-9)
        || (ovRec.sco === false && !!recSilas.sco)
        || (ovRec.sco === true && !recSilas.sco)
        || (ovRec.eco === false && !!recSilas.eco)
        || (ovRec.eco === true && !recSilas.eco)
        // The card's `recOverridden` has always counted a changed structure, and this
        // comparison must agree with it — the badge on screen and the header on the sheet
        // are one claim. `recSilas` is computed WITHOUT the agent's unit precisely so this
        // stays answerable; comparing against a Silas that already took the pick is circular.
        || (ovUnit && recSilas.unit && ovUnit !== String(recSilas.unit).toUpperCase())));
      const rec = ovDiffers
        ? recommendWheat(fips, county, aph, acres, price, vol, cvOf(series), series, FIPS_STATE[fips], rateY, adjY, uPrac,
            { incumbent: recInc, unitStructure: unitSrc ? unitSrc.unit : null, unit: ovUnit || curUnit,
              selection: { level: ovRec.cl, sco: ovRec.sco, eco: ovRec.eco }, quoteRP: RA.quoteRP,
              aphHistoryAcres: histRef.acres || null, aphHistoryYear: histRef.year || null })
        : recSilas;
      const rq = rec ? quoteRP({ ...bfrQ, state:fips, county, aph, rateYield:rateY, adjustedYield:adjY, cl:rec.cl, acres, unit:rec.unit, price, vol, practice:uPrac }) : null;
      if (levelSink && rec && rec.cl != null) levelSink[county] = rec.cl;
      // Written beside the level and from the SAME `rec`, so the two can never
      // describe different recommendations.
      if (recSink && rec) recSink[county] = rec;
      // The percentage this column quotes: the agent's own, where they moved the slider,
      // else the election the grower holds — the card's `rc = { ...recDflt, ...recCfg }`,
      // read the same way so screen and paper cannot price two different ECOs.
      const rScoPct = (ovRec && ovRec.scoPct != null) ? ovRec.scoPct : (cScoPct != null ? cScoPct : AREA_COVERAGE_PCT_DEFAULT);
      const rEcoPct = (ovRec && ovRec.ecoPct != null) ? ovRec.ecoPct : (cEcoPct != null ? cEcoPct : AREA_COVERAGE_PCT_DEFAULT);
      const rs = rec && rec.sco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"SCO", baseCl:rec.cl, aph, acres, price, vol, practice:uPrac, coveragePct: areaCoverageFractionOfPct(rScoPct) }) : null;
      const re = rec && rec.eco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"ECO", baseCl:rec.cl, aph, acres, price, vol, practice:uPrac, coveragePct: areaCoverageFractionOfPct(rEcoPct) }) : null;
      // ── PAGE 3's 2027 COLUMN CARRIES WHAT WE RECOMMEND THEY ADD ──────────────
      // `qs`/`qe` above are the "2027 Same" column and are gated on what the grower
      // carries TODAY. That is right on page 1, which has a separate Recommended
      // column beside it — and wrong on the comparison page, which has only ONE 2027
      // column. KOCH LIBRA carries ECO and no SCO; we recommend adding SCO, and the
      // page printed an em-dash in BOTH SCO cells, so the single layer the renewal
      // conversation is about was the one layer absent. Kevin, 2026-09-02.
      //
      // ⚠ THE WHOLE COLUMN IS QUOTED AT THE RECOMMENDED LEVEL, not at curCl. Kevin,
      // 2026-09-04: "comparison should be our rec vs what they had last year." This
      // page used to hold the grower's CURRENT level in both years, which was right
      // while the engine rarely moved anybody — and became a contradiction the day it
      // did: Sullivan Farms' Livingston sheet recommended 75% on page 1 and showed an
      // 80% column here, in one document.
      //
      // EVERY 2027 figure moves together — level, guarantee, MPCI, SCO, ECO and the
      // prevent-plant line. The earlier objection to mixing was about a MIXED column
      // (an 85% SCO beside an 80% MPCI); a column that moves as one is internally
      // consistent, and the level change becomes its own row rather than a footnote.
      const cmpCl = (rec && rec.cl != null) ? rec.cl : curCl;
      const cmpScoNew = !cSco && !!(rec && rec.sco);
      const cmpEcoNew = !cEco && !!(rec && rec.eco);
      // ⚠ AND THE COLUMN MUST FOLLOW THE SELECTION IN THE OTHER DIRECTION TOO.
      // Kevin, 2026-09-14: "I unselected SCO on a quote and then when I went to print it,
      // its still showing SCO. our override needs to be what prints."
      //
      // `qsCmp` fell back to `qs` — page 1's "2027 Same" quote, gated on what the grower
      // carries TODAY — whenever the recommendation did not include the layer. So dropping
      // SCO on a grower who already has it left this column pricing it anyway, and ONE
      // document then said two things: measured on KAST FARMS (Orleans, SCO unticked),
      // page 1's Selected column read $25.28/ac with SCO em-dashed while this page read
      // $30.96/ac including SCO at $5.68. The fallback only ever fired for a layer the
      // grower already had, which is exactly the case an agent drops.
      const cmpScoDropped = !!cSco && !!rec && !rec.sco;
      const cmpEcoDropped = !!cEco && !!rec && !rec.eco;
      // The MPCI quote for this column. `q` is page 1's "2027 Same" quote at curCl and
      // stays that; `rq` is already the recommended-level quote, so the column reuses it
      // rather than quoting a third time and risking the two disagreeing.
      const qCmp = (rec && rq) ? rq : q;
      // With a recommendation in hand the column IS the recommendation: `rs`/`re` are
      // already quoted at `rec.cl` (= cmpCl), and a layer it does not carry is null rather
      // than borrowing today's election. With no recommendation at all there is nothing to
      // follow, so the grower's own election still answers — unchanged.
      const qsCmp = rec ? (rec.sco ? rs : null) : qs;
      const qeCmp = rec ? (rec.eco ? re : null) : qe;
      // ⚠ THE PRIOR-YEAR PRICE HAS A THIRD RUNG, AND IT IS RMA'S OWN PUBLISHED NUMBER.
      // A coverage whose acreage was never keyed has no stored election and no billed
      // liability. Measured 2026-09-04: 89 of 131 CY2027 renewal units are in that state,
      // across 59 farms, and ALL 89 carry an election and an approved yield. The price is
      // announced, not modelled — CY2026 wheat is in `rma_prices` at $5.76 conventional and
      // $11.16 organic — and it is scaled by the PRIOR row's own price election (CAT 55%).
      //
      // ⚠ AND IT NOW OUTRANKS THE BACK-SOLVE (2026-09-18). The price used to be backed out of
      // the billed liability over the SUMMARY APH x level — 426.73 / 74.0 = $5.77 on Clark
      // Five — which folds RMA's per-database rounding into the price. $5.77 is on no document
      // he holds; the SOI and RMA both say $5.76. The billed LIABILITY is the fact, and the
      // method verified against seven SOIs derives the production guarantee FROM it:
      // liability / price = 74.08 bu/ac, the three databases each rounded to the tenth.
      // priorYearChain is the one rule; it is declared here, above the bars, so the bar, the
      // column header and page 3 read the same price instead of three copies of it.
      const px26 = pxPrevFor(uPrac, fips);
      const pe26Pct = (pRow && pRow.price_election_pct != null && +pRow.price_election_pct > 0 && +pRow.price_election_pct <= 1)
        ? +pRow.price_election_pct : 1;
      const chain26 = priorYearChain({ storedElection: pRow && pRow.price_election, publishedPrice: px26 ? px26.price : null,
        priceElectionPct: pe26Pct, liabPerAc: cur.mc, aph: curAph, cl: curCl });
      // The SAME bar set the screen shows: last year's actual, plus a 2027 bar at the
      // recommended level and its neighbours (wheatBarLevels never repeats a level, so
      // his current election shares a bar rather than drawing a duplicate).
      // ⚠ THE GATE IS WIDENED FOR THE FACT SINK, AND THE RENDER SITE IS PINNED SO THE
      // QUOTE DOCUMENT CANNOT MOVE. Kevin, 2026-09-23, on the Full Strategy Report:
      // "I want the attached coverage stack like the attached screenshot in the FSR" —
      // the screenshot is THIS row of bars, not the single annotated stack the report
      // has been drawing. `stacks` is the agent's own chip for the QUOTE, and the
      // report's pages are a fixed shape (the same reasoning that already forces
      // `lastYear` on for the compare page), so a chip left unticked must not delete a
      // chart from the grower's report. Building the bars when a sink is present makes
      // them available WITHOUT changing what the quote prints: every render site below
      // now tests `PC.stacks` itself, so with the chip off both were empty before and
      // are empty still.
      const pBars = (() => {
        if (!rec || !rq || !(PC.stacks || PC.factSink)) return [];
        const out = [];
        const cov26 = cur.mc, prem26 = cur.mp;
        if (PC.lastYear && prem26 > 0 && cov26 > 0) out.push({ cl: curCl, year: MPCI_WHEAT_YEAR - 1, tag: 'last', aph: curAph, price: chain26.price || price,
          // Last year's guarantee is the AIP's OWN — the billed liability over the published
          // price (chain26.guarBu). Re-deriving it from the prior APH and level would state a
          // figure the carrier never billed, which is the whole reason that chain exists.
          guarBu: chain26.guarBu,
          m: { prem: prem26, cov: cov26 },
          // A layer with no figures is not a layer — it was drawing a band labelled "— · —".
          sc: (cSco && (cur.sp != null || cur.sc != null)) ? { prem: cur.sp, cov: cur.sc } : null,
          ec: (cEco && (cur.ep != null || cur.ec != null)) ? { prem: cur.ep, cov: cur.ec } : null,
          prem: (cur.mp||0)+(cur.sp||0)+(cur.ep||0), ratio: (cur.mp > 0 && cov26 > 0) ? cov26/cur.mp : null });
        // THE SAME LEVELS THE SCREEN DRAWS: the agent's own selection for this unit, keyed
        // exactly as the card keys it, or the automatic set where they have not chosen.
        const selLv = PC.barSel ? PC.barSel[wheatUnitKey(r)] : undefined;
        for (const l of wheatChartLevels(rec.cl, curCl, LEVELS, selLv === undefined ? null : selLv)) {
          const m = quoteRP({ ...bfrQ, state:fips, county, aph, rateYield:rateY, adjustedYield:adjY, cl:l, acres, unit:rec.unit, price, vol, practice:uPrac });
          if (!m) continue;
          const sc = rec.sco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:'SCO', baseCl:l, aph, acres, price, vol, practice:uPrac, coveragePct: areaCoverageFractionOfPct(rScoPct) }) : null;
          const ec = rec.eco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:'ECO', baseCl:l, aph, acres, price, vol, practice:uPrac, coveragePct: areaCoverageFractionOfPct(rEcoPct) }) : null;
          const isRec = Math.abs(l - rec.cl) < 1e-9;
          out.push({ cl: l, year: 2027, tag: isRec ? (rec.agentSelected ? 'sel' : 'rec') : (curCl != null && Math.abs(l - curCl) < 1e-9 ? 'elected' : null),
            // COVERAGE always; the PREMIUM only where the unit structure is on file. Both bars
            // still draw, so a county with no structure shows what it protects and says nothing
            // about what it costs — rather than quoting the cheapest structure RMA sells.
            // ⚠ THE BUSHEL GUARANTEE IS THE ENGINE'S OWN `guarBu`, NOT `aph x cl`.
            // Kevin, 2026-09-22: "make sure bushel per acre guarantee gets printed on the
            // bar quotes for each coverage level." On a MULTI-DATABASE unit the production
            // guarantee is the per-database SUM with each database rounded to the tenth the
            // way RMA rounds it, so `aph x cl` off the blended APH differs from it by that
            // rounding — which is exactly the second calculation the guarantee chain below
            // already refuses to make.
            aph, price, guarBu: m.guarBu, m: { prem: unitPrem(m.netAcre), cov: m.liabPerAc },
            sc: sc ? { prem: unitPrem(sc.netAcre), cov: sc.covPerAc } : null, ec: ec ? { prem: unitPrem(ec.netAcre), cov: ec.covPerAc } : null,
            prem: premiumOk ? (m.netAcre||0)+(sc?sc.netAcre:0)+(ec?ec.netAcre:0) : null,
            ratio: (premiumOk && m.netAcre > 0 && m.liabPerAc > 0) ? m.liabPerAc/m.netAcre : null });
        }
        return out;
      })();
      const priceEl = chain26.price;
      // Whether the figures in the prior column came from the AIP's bill or from RMA's
      // published price. Coverage can be derived; PREMIUM CANNOT — it needs the prior
      // year's rate tables, which are not in this tree, and a 17.5% error beside
      // penny-exact figures is worse than an empty column. The page says which.
      const priorBilled = [cur.mp, cur.mc, cur.sp, cur.sc, cur.ep, cur.ec].some(v => v != null);
      const cell = (p, c) => `<td>${d2(p)}</td><td class="cov">${d2(c)}</td>`;
      const totP = (a,b,c)=>{const v=(a||0)+(b||0)+(c||0);return v||null;};
      const row = (lbl, cP,cC, sP,sC, rP,rC, bold) => `<tr${bold?' class="tot"':''}><td class="lbl">${lbl}</td>${hasPrior?cell(cP,cC):''}${cell(sP,sC)}${cell(rP,rC)}</tr>`;
      // LAST YEAR'S PRODUCTION GUARANTEE: the AIP's billed liability / the price (74.08 on Clark
      // Five), and APH x level only where nothing was billed. Two decimals kept for the arithmetic;
      // printed to the tenth.
      const curGuarBu = chain26.guarBu;
      const curGuar = curGuarBu != null ? curGuarBu.toFixed(1) : (Math.round(curAph*curCl*10)/10).toFixed(1);
      // LAST YEAR'S COVERAGE PER LAYER, through the SHARED rule below — the unit facts
      // and the side-by-side page state one number or they disagree about what a grower
      // carried. `cmpPage` keeps its own local binding rather than closing over this
      // one, because tools/check-wheat-compare-page.js extracts that function BY SOURCE
      // and runs it in an isolated scope: a free variable hoisted out of it renders
      // nothing there and every assertion fails at once. One RULE, two call sites.
      const erPrior = (curAph > 0 && priceEl > 0) ? curAph * priceEl : null;
      const cvPrior = wheatPriorCoverage(cur, erPrior, curCl, scoEcoBands(MPCI_WHEAT_YEAR - 1), cSco, cEco);
      const colHead = (yr, sub, recCol) => `<th colspan="2"${recCol?' class="rec-h"':''}>${yr}<div class="colsub">${sub}</div></th>`;
      // ── THE GUARANTEE CHAIN (work order B, 2026-09-18) ─────────────────────────────
      // RMA: production guarantee per acre = approved yield x coverage level; revenue
      // guarantee = production guarantee x projected price (x the price election — CAT 55%).
      // Every figure below is one the engine or the AIP already produced — q.guarBu, the
      // billed liability over the published price — never a second calculation. On a
      // multi-database unit the production guarantee is the per-database sum (each database
      // rounded to the tenth, as RMA does), so APH x level can differ from it by that rounding;
      // the line under the table shows the databases it came from.
      const gCell = (txt) => `<td colspan="2" style="text-align:center;font-size:7.8pt;color:#3a4a3a;padding:2px 4px">${txt}</td>`;
      const gRow = (lbl, a, b, c) => `<tr><td class="lbl" style="font-size:7.8pt;color:#5c6f5c;font-weight:600">${lbl}</td>${hasPrior ? gCell(a) : ""}${gCell(b)}${gCell(c)}</tr>`;
      // ⚠ AN EQUATION MAY ONLY BE PRINTED WHERE IT PRODUCES THE NUMBER BESIDE IT.
      // This printed "APH {stored} x {level}% = {guarantee}" on every column, and the
      // 2026 column's guarantee does not come from that equation — it is the AIP's
      // OWN billed figure, `liability / price`, which is RMA's Marketable Guarantee
      // and the number the SOI prints. On MEISENZAHL FARMS LLC / Monroe the two
      // methods disagree because the stored summary APH is the UNWEIGHTED average of
      // the unit APHs while the AIP's is acre-weighted:
      //
      //     billed   $348.73 / $5.76      = 60.54 bu  -> implies APH 80.7
      //     stored   81.0 x 75%           = 60.75 bu
      //
      // so one row read "APH 81.0 x 75% = 60.5 bu" beside "APH 81.0 x 75% = 60.8 bu",
      // and the year-over-year page printed APH "-" and coverage level "-" with the
      // guarantee moving +0.3 bu, which nothing on the page could explain.
      // BOTH NUMBERS ARE RIGHT. Only the label was, and it is the label that changes:
      // where the equation does not reproduce the printed guarantee, the guarantee is
      // stated with its PROVENANCE instead of with arithmetic that did not produce it.
      // This also retires the recorded multi-database case (Huntingdon printed
      // "APH 98.7 x 75% = 74.1", where 98.7 x 0.75 is 74.0 — the value is the correct
      // per-database sum and the equation was the simplification).
      // Compared on the ROUNDED figures, because that is what the reader sees.
      const pgTxt = (ap, cl, g, src) => {
        if (!(ap > 0 && cl > 0 && g != null)) return "&mdash;";
        const shown = (+g).toFixed(1);
        if ((+ap * cl).toFixed(1) === shown)
          return `APH ${(+ap).toFixed(1)} &times; ${Math.round(cl*100)}% = <b>${shown} bu</b>`;
        const why = src === "billed" ? "your billed guarantee" : "summed per APH database";
        return `<b>${shown} bu</b> <span style="color:#8a978a;font-weight:400">${why}</span>`;
      };
      const rgTxt = (g, px, rev) => (g != null && px > 0 && rev != null) ? `${(+g).toFixed(1)} bu &times; ${d2(px)} = <b>${d2(rev)}</b>` : "&mdash;";
      // A county that was elected but never billed has nothing to put in the prior-year
      // column. Printing it anyway spends a third of the table on em-dashes.
      // ⚠ "Last year" is a SECTION of the document, so switching it off takes the 2026
      // column with it — the table's colspans already follow `hasPrior`, which is why this
      // is the one place it has to be turned off rather than blanked cell by cell.
      const hasPrior = priorBilled && PC.lastYear;
      // ⚠ THE YEAR-OVER-YEAR PAGE ASKS A DIFFERENT QUESTION FROM PAGE 1's COLUMN, so it
      // gets its own gate. Page 1 compares DOLLARS side by side and is right to stay silent
      // without them. This page is about what CHANGED between two products — the coverage
      // level, the approved yield, the guarantee, the SCO/ECO band boundary moving 86 -> 90,
      // and the PF prevent-plant buy-up RMA withdrew — and every one of those is answerable
      // from an election, an approved yield and RMA's own published price. Gating it on
      // billed dollars suppressed it on 89 of 131 renewal units that could answer all of it.
      const cmpHasPrior = PC.comparePage && PC.lastYear
        && (priorBilled || !!(pRow && curCl != null && curAph > 0 && priceEl > 0));
      const polNum = pNumbers ? (pNumbers[String(r.policy_id)] || "") : "";
      // ── PAGE 3 · YEAR OVER YEAR ────────────────────────────────────────────────
      // Kevin, 2026-09-02: a plain side-by-side of premium, price-per-bushel guarantee
      // and coverage for MPCI / SCO / ECO — and, where the grower held the PF
      // prevent-plant buy-up RMA no longer sells, the difference STATED rather than
      // quietly absorbed into a smaller factor.
      //
      // It renders ONLY with a prior year. Comparing a renewal against nothing is a
      // page of em-dashes, which reads as a broken document rather than as new business.
      // The recommended level, as a number. cmpPage holds the grower's CURRENT election
      // in both years so the two are comparable, so it has to be able to say when that is
      // not what we are recommending.
      const recCl = rec && rec.cl != null ? rec.cl : null;
      // ── THE FACT SINK — the Full Strategy Report's only source ─────────────────
      // Every figure below was computed above, on this page, for this unit. The report
      // synthesises from these values and computes no coverage of its own, so the
      // standard report and the quote page it summarises cannot disagree.
      //
      // ⚠ A TOTAL WITH A MISSING LAYER IS UNKNOWN, NEVER CHEAPER. A recommended SCO whose
      // premium did not price makes the whole per-acre total null — the rule the canonical
      // strategy object already applies to area premium.
      if (PC.factSink && rec) {
        const layerTotal = (m, withSco, sq, withEco, eq) => {
          const parts = [m ? m.netAcre : null, withSco ? (sq ? sq.netAcre : null) : 0, withEco ? (eq ? eq.netAcre : null) : 0];
          return parts.some(v => v == null) ? null : parts.reduce((a, v) => a + v, 0);
        };
        const priorTotal = (cur.mp != null && (!cSco || cur.sp != null) && (!cEco || cur.ep != null))
          ? (cur.mp || 0) + (cSco ? cur.sp : 0) + (cEco ? cur.ep : 0) : null;
        PC.factSink[wheatUnitKey(r)] = {
          key: wheatUnitKey(r), county, state: FIPS_STATE[fips] || null, policyNumber: polNum || null,
          // ⚠ CV AND BASIS COME FROM HERE, NOT FROM THE REPORT. Both are computed
          // above off THIS unit's own series and FIPS; a report that re-derived
          // them would need the production history and would reach for component
          // state, which in a BATCH print is another farm's. Same reason the
          // stack and the premium are emitted rather than recomputed.
          // A null CV means fewer than three years, never "steady".
          cvPct: cvPct != null ? +cvPct.toFixed(1) : null,
          countyExpBu: ctyExpBu != null ? +ctyExpBu : null,
          yieldBasis: yieldBasis != null ? +yieldBasis.toFixed(3) : null,
          // The POLICY this unit sits on, so a caller that wants to STATE a unit structure can
          // build `wheatPlannedKey` (policy_id | county | practice) from the fact alone. Without
          // it the report can name what is missing and cannot offer to set it.
          policyId: r.policy_id || null,
          // ⚠ THE STACK IS BUILT HERE BECAUSE THIS IS WHERE THE LAYERS EXIST. Kevin,
          // 2026-09-23: page one of the report is the COVERAGE STACK. `_rptStackHtml`
          // is the print twin of the on-screen CoverageStackViz — the same bands with
          // the premium and coverage of each layer — and it needs the MPCI, SCO and
          // ECO quotes, which live in this closure and nowhere else. Emitting it into
          // the fact sink hands the report the SAME stack the quote page draws rather
          // than a second one built from summary totals, which could not carry the
          // per-layer dollars at all: page one used to show only the grid's totals, so
          // a reader could not see what SCO cost or what it bought.
          stackHtml: (rec && rq) ? _rptStackHtml({ bandYear: MPCI_WHEAT_YEAR, cl: rec.cl, aph, price,
            mpci: { prem: unitPrem(rq.netAcre), cov: rq.liabPerAc },
            sco: rs ? { prem: unitPrem(rs.netAcre), cov: rs.covPerAc } : null,
            eco: re ? { prem: unitPrem(re.netAcre), cov: re.covPerAc } : null,
            arcplc: (arcByCounty && arcByCounty[(FIPS_STATE[fips]||"") + "|" + county]) || null }) : null,
          // ⚠ THE BAR ROW IS THE SAME OBJECT THE QUOTE DRAWS, NOT A SECOND ONE. `pBars`
          // already holds last year's ACTUAL bar plus the 2027 quote at the recommended
          // level and its neighbours, each priced layer by layer — so the report's page
          // one gets the chart Kevin screenshotted rather than a reimplementation that
          // would drift from it. Null below two bars: one bar compares nothing, and the
          // page falls back to the single stack the way the quote sheet already does.
          barsHtml: pBars.length > 1
            ? _rptBarsHtml({ bars: pBars, guideAt: rec ? rec.cl : null,
                             premScale: Math.max(0, ...pBars.map(b => b.prem || 0)) })
            : null,
          practiceCode: uPrac, practiceLabel: uPrac === "003" ? null : (WHEAT_PRACTICE_NAMES[uPrac] || uPrac),
          cropYear: MPCI_WHEAT_YEAR, priorYear: MPCI_WHEAT_YEAR - 1,
          aph, aphYear: r._aphYear || null,
          // HOW that APH was built — the same note the quote page prints beside it (one database,
          // or a weighted blend and on which year's yields). The report repeats it rather than
          // re-deriving it, so page one and the quote cannot describe the APH two ways.
          aphBasis: { source: RA.source || null, note: aphBasisNote({ source: RA.source, blend: RA.blend, quoteYear: MPCI_WHEAT_YEAR }) || null,
                      databases: RA.blend && RA.blend.n != null ? RA.blend.n : null,
                      // how many of those databases still stand on LAST year's approved yield — the
                      // unit's crop year is not the year of a blend built entirely on 2026 yields
                      priorYieldDatabases: RA.blend && RA.blend.priorYieldCount != null ? RA.blend.priorYieldCount : null },
          // The unit structure the premium rests on, with the year it is from — or null, and the
          // report withholds the premium rather than price a structure nobody stated.
          // ⚠ AN AGENT-STATED UNIT STRUCTURE COUNTS. `unitKnown` above has included
          // `unitEntered` since an agent was given the ability to state one the AIP has
          // not keyed — the premium IS quoted off it — but this field recorded only the
          // AIP's, so the unit facts said "unknown" for a unit the document had just
          // priced. Nothing read it hard enough to notice until the customer report
          // began REFUSING to print without one, at which point supplying the structure
          // would not have cleared the refusal.
          // ⚠ THIS RECORDS WHAT THE DOCUMENT PRICED ON, NOT WHAT THE POLICY IS ON, and the
          // two are now routinely different: the quote basis is Enterprise by agency
          // convention while the file may state Basic. Reporting the file value here would
          // caption a sheet with a structure it did not use. `onPolicy` carries the file
          // value separately so nothing is lost.
          unit: { value: curUnit,
                  year: unitEntered ? null : (unitSrc ? unitSrc.year : null),
                  stated: !!unitEntered,
                  source: unitEntered ? "agent" : "agency-default",
                  onPolicy: unitSrc ? unitSrc.unit : null,
                  onPolicyYear: unitSrc ? unitSrc.year : null },
          // Whether wheat was planted here last year, per the AIP's acreage record (null: not stated).
          priorPlanted: wheatPlantedIn(r, pRow, PC.aipAcreage, pNumbers, MPCI_WHEAT_YEAR - 1),
          vol: { value: vol != null ? +vol : null, status: uPx.volStatus || null },
          yieldRange: series.length ? { first: series[0].yr, last: series[series.length - 1].yr, years: series.map(s => s.yr) } : null,
          price: { value: price, status: uPx.status || null },
          acreage: { acres: basis.acres, source: basis.source,
                     year: basis.source === ACREAGE_SOURCE.PRIOR_YEAR ? basis.reference.priorYearOf
                         : (basis.source === ACREAGE_SOURCE.REPORTED || basis.source === ACREAGE_SOURCE.PLANNED) ? MPCI_WHEAT_YEAR : null },
          // The grower's SOURCED election — never curCl, which carries a 0.75 display fallback.
          // ⚠ THE ELECTED PERCENTAGE TRAVELS WITH THE COVERAGE. SCO/ECO are bought at 50-100%
          // of the band, and a page that prints "ECO 90-95%" for a grower holding half of it
          // describes coverage he does not have. The premium was always right; the words were
          // not. Measured 2026-09-17: 11 CY2026 wheat growers hold ECO at 50%, one at 75%,
          // one at 90%.
          current: { level: incSourced, unit: unitSrc ? unitSrc.unit : null, plan: (pRow && pRow.plan) || null, sco: !!cSco, eco: !!cEco,
            scoPct: cSco ? cScoPct : null, ecoPct: cEco ? cEcoPct : null,
            billedPrior: priorBilled ? { total: priorTotal } : null,
            // LAST YEAR, so page one can print "what you had -> what we propose ->
            // what changes" without re-deriving either side. `derived` says whether the
            // coverage came from the AIP's bill or from RMA's published price; the
            // PREMIUM is only ever the billed one, because the prior year's rate tables
            // are not in this tree.
            priorGuaranteeBu: curGuarBu != null ? curGuarBu : null,
            priorCoverage: cvPrior.mc == null ? null
              : { mpci: cvPrior.mc, sco: cvPrior.sc, eco: cvPrior.ec, derived: !priorBilled },
            at2027: q ? { total: layerTotal(q, !!cSco, qs, !!cEco, qe), guaranteeBu: q.guarBu, coverageMpci: q.liabPerAc,
              // Per layer, so a change can be split into the layers that actually move.
              mpci: q.netAcre, sco: cSco ? (qs ? qs.netAcre : null) : null, eco: cEco ? (qe ? qe.netAcre : null) : null } : null,
            // The current LEVEL carrying the PROPOSED county coverage — priced here, with the
            // same quote functions as every column above — so a change that both adds SCO and
            // raises the level can say how much of the difference is each.
            withProposedArea: (incSourced != null && rq) ? (() => {
              const m = quoteRP({ ...bfrQ, state:fips, county, aph, rateYield:rateY, adjustedYield:adjY, cl:incSourced, acres, unit:rec.unit, price, vol, practice:uPrac });
              // ⚠ THE PERCENTAGE IS THE PROPOSED ONE, because this column IS the proposal —
              // the county coverage we recommend, priced at the level the grower already holds,
              // so the change can be split into "the level moved" and "the county layer was
              // added". Quoting it at 100% of the band while the proposal beside it quotes the
              // elected 50-100% would make the split arithmetic disagree with both columns it
              // sits between (#194 made the election quotable; this call predates it).
              const sq = rec.sco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"SCO", baseCl:incSourced, aph, acres, price, vol, practice:uPrac, coveragePct: areaCoverageFractionOfPct(rScoPct) }) : null;
              const eq = rec.eco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"ECO", baseCl:incSourced, aph, acres, price, vol, practice:uPrac, coveragePct: areaCoverageFractionOfPct(rEcoPct) }) : null;
              return m ? { total: layerTotal(m, !!rec.sco, sq, !!rec.eco, eq) } : null;
            })() : null },
          proposed: { level: rec.cl, unit: rec.unit, plan: "RP", sco: !!rec.sco, eco: !!rec.eco,
            scoPct: rec.sco ? rScoPct : null, ecoPct: rec.eco ? rEcoPct : null,
            premium: rq ? { mpci: rq.netAcre, sco: rs ? rs.netAcre : null, eco: re ? re.netAcre : null,
                            total: layerTotal(rq, !!rec.sco, rs, !!rec.eco, re) } : null,
            guaranteeBu: rq ? rq.guarBu : null,
            coverage: rq ? { mpci: rq.liabPerAc, sco: rs ? rs.covPerAc : null, eco: re ? re.covPerAc : null } : null,
            ppPerAc: rq ? rq.ppLiabPerAc : null },
          selectedBy: rec.agentSelected ? "agent" : "silas",
          engine: { classification: rec.classification, bandDecision: rec.bandDecision || null,
            yieldYears: rec.yieldRecord ? rec.yieldRecord.years : null, nextLevelUp: rec.nextLevelUp || null,
            recMove: rec.recMove || null, premiumWithheld: !!rec.premiumWithheld,
            // The farm's own short-crop years (yield below lossLevel x APH), for the county record's
            // plain sentence: did county coverage pay in the years this farm fell short?
            areaFit: rec.fit ? { lossLevel: rec.fit.lossLevel != null ? rec.fit.lossLevel : null,
              lossYears: rec.fit.lossYears || [], nLossYears: rec.fit.nLossYears != null ? rec.fit.nLossYears : null } : null,
            // The engine's own words, kept for the appendix's technical notes ONLY.
            why: rec.why || [], keepReason: rec.keepReason || null },
          pp: { factor: uPpf.base, factorPrior: prevPpf, buyUpHeldPrior: !!prevPp.applied,
                buyUpOfferedNow: uPpf.base != null ? !!uPpf.pfOffered : null },
          bands2026: scoEcoBands(MPCI_WHEAT_YEAR - 1), bands2027: scoEcoBands(MPCI_WHEAT_YEAR),
        };
      }
      const cmpPage = !cmpHasPrior ? "" : (() => {
        const b26 = scoEcoBands(MPCI_WHEAT_YEAR - 1), b27 = scoEcoBands(MPCI_WHEAT_YEAR);
        // Expected revenue is what the AREA bands are a percentage OF, and it is the farm's
        // own APH x that year's price — the same product quoteAreaRP2027 rates against.
        // Printing it lets the reader multiply a band by it and land on the coverage cell.
        const er26 = (curAph > 0 && priceEl > 0) ? curAph * priceEl : null;
        const cv26 = wheatPriorCoverage(cur, er26, curCl, b26, cSco, cEco);
        // ⚠ COVERAGE IS DERIVABLE FROM THE PRICE; PREMIUM IS NOT. Every layer's coverage is
        // a band of expected revenue — MPCI is 0 to the elected level, SCO the level to its
        // top, ECO its own two points — so with the approved yield, the election and RMA's
        // published price they are arithmetic, and the same arithmetic `quoteAreaRP2027`
        // rates against. The PREMIUM needs the prior year's rate tables, which are not in
        // this tree; modelling it would put a ~17.5% error beside penny-exact 2027 figures
        // on a document a grower signs, so those cells stay blank and the page says why.
        //
        // A BILLED FIGURE ALWAYS WINS. This fills only where the AIP keyed nothing, so a
        // policy that actually billed is untouched — the same rule the on-screen prior-year
        // bar already follows.
        //
        // ⚠ AND AN ENDORSEMENT HE DID NOT CARRY GETS NOTHING. `cSco`/`cEco` are the prior
        // year's own rows; deriving a band for a layer that is absent would invent coverage
        // he never bought, which is the direction that flatters the comparison.
        const cvDerived = !priorBilled && cv26.mc != null;
        const er27 = (aph > 0 && price > 0) ? aph * price : null;
        const pts = (lo, hi) => Math.round((hi - lo) * 100);
        const sgn = (d, fmt) => (d == null || Math.abs(d) < 0.005) ? "&mdash;"
          : (d > 0 ? "+" : "&minus;") + fmt(Math.abs(d));
        const dCash = (a, b) => (a == null || b == null) ? "&mdash;" : sgn(b - a, d2);
        const cRow = (lbl, a, b, chg, cls) => "<tr" + (cls ? " class=\"" + cls + "\"" : "") + ">"
          + "<td class=\"lbl\">" + lbl + "</td><td class=\"cov\">" + a + "</td>"
          + "<td class=\"cov\">" + b + "</td><td class=\"cov\">" + chg + "</td></tr>";
        const hdr = t => "<tr class=\"tot\"><td class=\"lbl\" colspan=\"4\" style=\"text-align:left;font-size:7.5pt;letter-spacing:.06em;text-transform:uppercase\">" + t + "</td></tr>";
        // The prior column is what the AIP BILLED; the quote column is an estimate. Both
        // are per acre so the subtraction is sound, but the footer says which is which
        // rather than letting the renewal read as already priced.
        // A layer the grower does not carry has no 2026 figure to subtract from, so its
        // change reads NEW rather than a plus sign against nothing — and the totals
        // below include it, because that is what the recommendation actually costs.
        const newTag = '<span style="color:#1f5c2e;font-weight:800;font-size:7.5pt;letter-spacing:.04em">NEW</span>';
        // ⚠ AND THE MIRROR: a layer this quote DROPS. Without a tag its cells are two
        // em-dashes, which on a table full of em-dashes reads as "not priced" rather than
        // as "not bought" — and the reader cannot tell the quote made a choice.
        const dropTag = '<span style="color:#8a5a00;font-weight:800;font-size:7.5pt;letter-spacing:.04em">DROPPED</span>';
        const droppedTxt = [cmpScoDropped ? "SCO" : null, cmpEcoDropped ? "ECO" : null].filter(Boolean).join(" and ");
        const addedTxt = [cmpScoNew ? "SCO" : null, cmpEcoNew ? "ECO" : null].filter(Boolean).join(" and ");
        const lvlTxt = `${Math.round(curCl*100)}%${unitSrc ? " " + (uLbl[curUnit]||curUnit) : ""}`;
        // The heading used to read "same election both years" whenever the level held —
        // which is what it printed over a column that had just dropped SCO.
        const metaChange = (Math.abs(cmpCl - curCl) > 1e-9
            ? `your ${Math.round(curCl*100)}% election against the ${Math.round(cmpCl*100)}%${unitSrc ? " " + (uLbl[curUnit]||curUnit) : ""} we recommend`
            : (addedTxt || droppedTxt) ? `${lvlTxt} both years` : `same election both years &middot; ${lvlTxt}`)
          + (addedTxt ? `, plus the ${addedTxt} we recommend adding` : "")
          + (droppedTxt ? `, dropping ${droppedTxt}` : "");
        const p26 = totP(cur.mp, cur.sp, cur.ep), p27 = totP(unitPrem(qCmp && qCmp.netAcre), unitPrem(qsCmp && qsCmp.netAcre), unitPrem(qeCmp && qeCmp.netAcre));
        const c26 = totP(cv26.mc, cv26.sc, cv26.ec), c27 = totP(qCmp && qCmp.liabPerAc, qsCmp && qsCmp.covPerAc, qeCmp && qeCmp.covPerAc);
        const pp26 = (cv26.mc != null && prevPpf != null) ? cv26.mc * prevPpf : null;
        const pp27 = qCmp ? qCmp.ppLiabPerAc : null;
        // What the withdrawn buy-up would have been worth on the quote year. This is
        // arithmetic on a factor that is NOT PURCHASABLE for that year, so it is labelled
        // as such and never enters a total — it exists only to size what RMA took away.
        // ⚠ THE BAND NOTE DEPENDS ON WHETHER HE ACTUALLY CARRIED SCO. Written for a
        // grower who holds both layers, it says "your SCO band widens from 6 points to
        // 10" and "15 points of area coverage in both years" — and KOCH LIBRA carried
        // ECO alone, so both sentences are false about him: he had no SCO band, and his
        // 2026 area coverage was ECO's 9 points. The version below is the one that
        // makes the recommendation land anyway: the 4 points that left ECO are exactly
        // what moved into SCO, which is the layer we are proposing he add.
        // ⚠ THE TABLE AND THE PROSE MUST ASK THE SAME QUESTION, AND THEY DID NOT.
        // This read `cur.sc != null` — "the AIP BILLED an SCO figure" — while the
        // table two blocks down prints `cv26.sc`, which falls back to COMPUTING the
        // band off the 2026 ELECTION (`cSco`) whenever the policy was never keyed.
        // For a county in exactly that state the page printed a $40.55 SCO row and
        // then said underneath it "they moved into SCO, which you did not carry in
        // 2026" — a table and a sentence disagreeing about the same fact on one
        // page. Measured on MEISENZAHL FARMS LLC / Ontario, whose 2026 policy
        // carries a plan-32 SCO row that the AIP never priced.
        // Deriving the gate from the VALUE THE TABLE PRINTS makes them agree by
        // construction: the prose can never deny a figure the page is showing.
        const hadSco26 = cv26.sc != null;
        // ⚠ THE SENTENCE WAS WRITTEN FOR A COMPARISON AT THE SAME COVERAGE LEVEL,
        // and it is printed beside one that need not be. It said "your SCO band
        // WIDENS from 6 points to 5" — which is a narrowing — and closed with
        // "so ECO falling is the split moving, not coverage lost" while the area
        // total went 15 points to 10. Both were true only while the 2027 column
        // sat at the same level as 2026; the recommended column moves it, and the
        // prose did not follow. Live on KAST FARMS 2026-09-05, comparing his 80%
        // against a recommended 85%.
        //
        // Derived from the two figures rather than assumed, so the verb and the
        // conclusion cannot drift from the numbers printed in the same sentence.
        // ⚠ A LAYER THE QUOTE DROPS HAS NO ${MPCI_WHEAT_YEAR} BAND, and this sentence must
        // not describe one. Read off what the column actually carries, not off the lattice.
        const _sco26 = pts(curCl, b26.scoTop), _sco27 = qsCmp ? pts(cmpCl, b27.scoTop) : 0;
        const _eco26 = pts(b26.ecoBottom, b26.ecoTop), _eco27 = qeCmp ? pts(b27.ecoBottom, b27.ecoTop) : 0;
        const _area26 = _sco26 + _eco26, _area27 = _sco27 + _eco27;
        const _verb = (a, b2) => b2 > a ? "widens" : b2 < a ? "narrows" : "is unchanged at";
        const _bandTxt = (name, a, b2) => b2 === 0
          ? `${name} is not in this quote (${a} points last year)`
          : `${name} ${_verb(a, b2)} ${a} points to ${b2}`;
        const _areaClose = droppedTxt
          // "those points moved from the county trigger to yours" is true of a LEVEL move
          // and false of a drop: nothing picks the band up, and the page has to say so.
          ? `the area band is ${_area26 - _area27} points smaller because this quote drops ${droppedTxt} &mdash; that band is not covered by anything else here.`
          : _area27 === _area26
          ? "so ECO falling is the split moving, not coverage lost."
          : _area27 < _area26
            ? `the area band is ${_area26 - _area27} points smaller because the ${Math.round(cmpCl*100)}% column carries more of the risk on your OWN policy: those points moved from the county trigger to yours, they were not dropped.`
            : `the area band is ${_area27 - _area26} points larger at the ${Math.round(cmpCl*100)}% column.`;
        const bandNote = b26.scoTop === b27.scoTop ? "" : hadSco26
          ? `<div class="note"><b>SCO and ECO changed shape for ${MPCI_WHEAT_YEAR}.</b>
        They are county-index bands stacked above your own policy, and RMA moved the boundary between them from ${Math.round(b26.scoTop*100)}% to ${Math.round(b27.scoTop*100)}%.
        Your ${_bandTxt("SCO band", _sco26, _sco27)}; ${_bandTxt("ECO", _eco26, _eco27)}.
        Together they reach ${Math.round(b27.ecoTop*100)}% &mdash; <b>${_area26} points of area coverage last year, ${_area27} this year</b> &mdash; ${_areaClose}</div>`
          : `<div class="note"><b>Your ECO band is smaller for ${MPCI_WHEAT_YEAR}, and the points moved into SCO.</b>
        SCO and ECO are county-index bands stacked above your own policy. RMA moved the boundary between them from ${Math.round(b26.scoTop*100)}% to ${Math.round(b27.scoTop*100)}%, so ECO narrows from ${pts(b26.ecoBottom, b26.ecoTop)} points to ${pts(b27.ecoBottom, b27.ecoTop)} &mdash; that is why the ECO figure falls.
        Those ${pts(b26.ecoBottom, b26.ecoTop) - pts(b27.ecoBottom, b27.ecoTop)} points did not disappear; they moved into SCO, which you did not carry in ${MPCI_WHEAT_YEAR - 1}.${cmpScoNew ? ` Adding SCO picks them up and the ${pts(cmpCl, b26.scoTop)} points below them as well &mdash; ${pts(cmpCl, b27.scoTop)} points of area coverage in all.` : ""}</div>`;
        const lostPf = (prevPp.applied && uPpf.base != null && !uPpf.pfOffered && qCmp)
          ? { at26: qCmp.liabPerAc * prevPpf, gap: qCmp.liabPerAc * (prevPpf - uPpf.base) } : null;
        return `
      <section class="page cmp">
        <div class="brand"><img class="logo" src="${logoSrc}" alt="Clover Ag Risk Management" /><div class="bs">Risk Management &middot; Spartan Insurance Agency LLC</div></div>
        <div class="qt">${MPCI_WHEAT_YEAR - 1} to ${MPCI_WHEAT_YEAR} &mdash; side by side</div>
        <div class="farm">${esc(farmName)} &mdash; ${esc(capCty(r.county))} County, ${esc(FIPS_STATE[fips]||"")}</div>
        <div class="meta">${polNum?`Policy ${esc(polNum)} &middot; `:""}${metaChange} &middot; RP &middot; ${esc(WHEAT_PRACTICE_NAMES[uPrac] || uPrac)}</div>
        <table>
          <thead><tr><th></th><th>${MPCI_WHEAT_YEAR - 1} actual</th><th>${MPCI_WHEAT_YEAR} quoted</th><th>Change</th></tr></thead>
          <tbody>
            ${cRow("Coverage level", Math.round(curCl*100) + "%", Math.round(cmpCl*100) + "%", Math.abs(cmpCl - curCl) < 1e-9 ? "&mdash;" : sgn(Math.round(cmpCl*100) - Math.round(curCl*100), v => v.toFixed(0) + " pts"))}
            ${cRow("Approved yield (APH)", curAph.toFixed(1) + " bu", aph.toFixed(1) + " bu", sgn(aph - curAph, v => v.toFixed(1) + " bu"))}
            ${cRow("Guarantee", curGuar + " bu/ac", qCmp ? qCmp.guarBu.toFixed(1) + " bu/ac" : "&mdash;", qCmp ? sgn(qCmp.guarBu - +curGuar, v => v.toFixed(1) + " bu") : "&mdash;")}
            ${cRow("Price", priceEl != null ? d2(priceEl) + "/bu" : "&mdash;", d2(price) + "/bu", priceEl != null ? sgn(price - priceEl, d2) : "&mdash;")}
            ${cRow("Expected revenue", er26 != null ? d2(er26) + "/ac" : "&mdash;", er27 != null ? d2(er27) + "/ac" : "&mdash;", dCash(er26, er27))}
            ${hdr("Coverage &middot; $ per acre")}
            ${cRow("MPCI", d2(cv26.mc), d2(qCmp && qCmp.liabPerAc), dCash(cv26.mc, qCmp && qCmp.liabPerAc))}
            ${cRow("SCO <span style=\"color:#8aa08a;font-weight:400\">" + (cmpScoNew ? Math.round(cmpCl*100) + "&ndash;" + Math.round(b27.scoTop*100) + "%" : cmpScoDropped ? pts(curCl, b26.scoTop) + " pts &rarr; none" : !qsCmp ? "" : pts(curCl, b26.scoTop) + " pts &rarr; " + pts(cmpCl, b27.scoTop) + " pts") + "</span>" + (cmpScoNew ? " " + newTag : cmpScoDropped ? " " + dropTag : ""), d2(cv26.sc), d2(qsCmp && qsCmp.covPerAc), cmpScoNew ? newTag : cmpScoDropped ? dropTag : dCash(cv26.sc, qsCmp && qsCmp.covPerAc))}
            ${cRow("ECO <span style=\"color:#8aa08a;font-weight:400\">" + (cmpEcoNew ? Math.round(b27.ecoBottom*100) + "&ndash;" + Math.round(b27.ecoTop*100) + "%" : cmpEcoDropped ? pts(b26.ecoBottom, b26.ecoTop) + " pts &rarr; none" : !qeCmp ? "" : pts(b26.ecoBottom, b26.ecoTop) + " pts &rarr; " + pts(b27.ecoBottom, b27.ecoTop) + " pts") + "</span>" + (cmpEcoNew ? " " + newTag : cmpEcoDropped ? " " + dropTag : ""), d2(cv26.ec), d2(qeCmp && qeCmp.covPerAc), cmpEcoNew ? newTag : cmpEcoDropped ? dropTag : dCash(cv26.ec, qeCmp && qeCmp.covPerAc))}
            ${cRow("Total coverage", d2(c26), d2(c27), dCash(c26, c27), "tot")}
            ${hdr("Premium you pay &middot; $ per acre")}
            ${cRow("MPCI", d2(cur.mp), d2(unitPrem(qCmp && qCmp.netAcre)), dCash(cur.mp, unitPrem(qCmp && qCmp.netAcre)))}
            ${cRow("SCO" + (cmpScoNew ? " " + newTag : cmpScoDropped ? " " + dropTag : ""), d2(cur.sp), d2(unitPrem(qsCmp && qsCmp.netAcre)), cmpScoNew ? newTag : cmpScoDropped ? dropTag : dCash(cur.sp, unitPrem(qsCmp && qsCmp.netAcre)))}
            ${cRow("ECO" + (cmpEcoNew ? " " + newTag : cmpEcoDropped ? " " + dropTag : ""), d2(cur.ep), d2(unitPrem(qeCmp && qeCmp.netAcre)), cmpEcoNew ? newTag : cmpEcoDropped ? dropTag : dCash(cur.ep, unitPrem(qeCmp && qeCmp.netAcre)))}
            ${cRow("Total premium", d2(p26), d2(p27), dCash(p26, p27), "tot")}
            ${uPpf.base == null ? "" : hdr("Prevent plant")
              + cRow("Guarantee factor", Math.round(prevPpf*100) + "%" + (prevPp.applied ? " (PF buy-up)" : ""), Math.round(uPpf.base*100) + "%" + (uPpf.pfOffered ? "" : " (no buy-up sold)"), sgn(Math.round(uPpf.base*100) - Math.round(prevPpf*100), v => v.toFixed(0) + " pts"))
              + cRow("Coverage on prevented acres", d2(pp26), d2(pp27), dCash(pp26, pp27))}
          </tbody>
        </table>
        ${(cmpScoNew || cmpEcoNew) ? `<div class="note"><b>${[cmpScoNew?"SCO":null,cmpEcoNew?"ECO":null].filter(Boolean).join(" and ")} ${(cmpScoNew && cmpEcoNew) ? "are" : "is"} new coverage, not carried in ${MPCI_WHEAT_YEAR - 1}.</b>
        Marked NEW above and quoted at the ${Math.round(cmpCl*100)}% level we recommend. The totals include it: that is what the addition costs and what it buys.
        ${Math.abs(cmpCl - curCl) > 1e-9
          ? `The ${MPCI_WHEAT_YEAR} column is our recommendation, not a like-for-like &mdash; it is quoted at <b>${Math.round(cmpCl*100)}%</b> against the <b>${Math.round(curCl*100)}%</b> you carried, so the coverage-level row above shows that change too.`
          : `That makes the ${MPCI_WHEAT_YEAR} column what we are recommending you carry rather than a like-for-like of today's election.`}</div>` : ""}
        ${droppedTxt ? `<div class="note"><b>${droppedTxt} ${cmpScoDropped && cmpEcoDropped ? "are" : "is"} not part of the ${MPCI_WHEAT_YEAR} quote.</b>
        You carried ${cmpScoDropped && cmpEcoDropped ? "them" : "it"} in ${MPCI_WHEAT_YEAR - 1}${(cmpScoDropped && cur.sp != null) ? ` (SCO at ${d2(cur.sp)}/ac)` : ""}${(cmpEcoDropped && cur.ep != null) ? ` (ECO at ${d2(cur.ep)}/ac)` : ""}; this quote leaves ${cmpScoDropped && cmpEcoDropped ? "them" : "it"} off, so the ${MPCI_WHEAT_YEAR} column carries no ${droppedTxt} premium and none of its coverage, and both totals above are lower by that much.
        ${droppedTxt} pays on the COUNTY's revenue, so dropping it removes county-trigger protection above your own coverage level &mdash; it does not change what your own policy pays.</div>` : ""}
        ${lostPf ? `<div class="note"><b>The prevent-plant buy-up you carried in ${MPCI_WHEAT_YEAR - 1} is not sold for ${MPCI_WHEAT_YEAR}.</b>
        Your ${MPCI_WHEAT_YEAR - 1} election included option <b>PF</b>, which raised the prevented-planting guarantee from ${Math.round(uPpf.base*100)}% to ${Math.round(prevPpf*100)}%.
        RMA publishes no buy-up for ${MPCI_WHEAT_YEAR} wheat in this county, so ${Math.round(uPpf.base*100)}% is the only factor available &mdash; a product change, not a choice on this quote.
        At last year's ${Math.round(prevPpf*100)}% the ${MPCI_WHEAT_YEAR} figure would be ${d2(lostPf.at26)}/ac, so the withdrawn points are worth <b>${d2(lostPf.gap)}/ac</b> of prevent-plant coverage.</div>` : ""}
        ${cvDerived ? `<div class="note"><b>Your ${MPCI_WHEAT_YEAR - 1} policy was never priced in the AIP's file for this county.</b>
        No acres, liability or premium was keyed on it, so <b>the ${MPCI_WHEAT_YEAR - 1} premium cannot be shown</b> &mdash; pricing it would need last year's rate tables and any figure we produced would be an estimate sitting beside penny-exact ${MPCI_WHEAT_YEAR} numbers.
        The ${MPCI_WHEAT_YEAR - 1} <b>coverage</b> above is exact: your own election (${Math.round(curCl*100)}%) and approved yield (${curAph.toFixed(1)} bu) at RMA's published ${MPCI_WHEAT_YEAR - 1} price of ${d2(priceEl)}/bu.</div>` : ""}
        ${(() => {
          // SCO and ECO figures above are the share of each band elected, and that share
          // can differ between the two columns. Said only where a column is below 100%.
          const lines = [["SCO", !!cSco, cScoPct, !!qsCmp, rScoPct], ["ECO", !!cEco, cEcoPct, !!qeCmp, rEcoPct]]
            .filter(([, h26, p26, h27, p27]) => (h26 && p26 != null && p26 < 100) || (h27 && p27 != null && p27 < 100))
            .map(([l, h26, p26, h27, p27]) => `${l} ${h26 ? (p26 == null ? "not stated" : p26 + "%") : "not carried"} in ${MPCI_WHEAT_YEAR - 1}, ${h27 ? p27 + "%" : "not quoted"} in ${MPCI_WHEAT_YEAR}`);
          return lines.length ? `<div class="note"><b>SCO and ECO are figured on the share of each band elected.</b> RMA lets a grower insure 50&ndash;100% of each band; coverage and premium both scale with it. ${lines.join("; ")}.</div>` : "";
        })()}
        ${bandNote}
        ${/* ⚠ SAME APH, SAME COVERAGE LEVEL, AND THE GUARANTEE MOVES. This table
              prints the APH row as "-" and the coverage-level row as "-" while the
              guarantee changes, which is arithmetically impossible if the guarantee
              is APH x level — and nothing on the page explained it. It is not an
              error in either number: the prior column is the AIP's BILLED guarantee
              (liability / price, acre-weighted across the policy's APH databases)
              and the quote column is this year's approved yield x the level. The
              note fires ONLY where the contradiction is actually visible, so it
              cannot become boilerplate on farms whose two methods agree. */
          (qCmp && curGuar != null && Math.abs(aph - curAph) < 0.05 &&
           Math.abs(qCmp.guarBu - +curGuar) >= 0.05)
            ? `<div class="note"><b>Your approved yield did not change, and the guarantee did.</b>
        The ${MPCI_WHEAT_YEAR - 1} figure is the guarantee your AIP actually billed &mdash; liability divided by that year's
        price, weighted across the APH databases on the policy. The ${MPCI_WHEAT_YEAR} figure is quoted from your approved
        yield of ${aph.toFixed(1)} bu at ${Math.round(curCl*100)}%. Both are correct; they are measured differently, which is
        the whole of the ${(qCmp.guarBu - +curGuar > 0 ? "+" : "−")}${Math.abs(qCmp.guarBu - +curGuar).toFixed(1)} bu difference.</div>`
            : ""}
        <div class="disc">${MPCI_WHEAT_YEAR - 1} column is ${cvDerived ? `coverage at RMA's published ${MPCI_WHEAT_YEAR - 1} price; no premium was billed` : "what the AIP billed"}${(pAcres && acres && Math.abs(pAcres - acres) > 0.5) ? ` on ${pAcres.toFixed(0)} ac` : ""}; ${MPCI_WHEAT_YEAR} is an estimate on RMA ${MPCI_WHEAT_YEAR} rates, ${acresPending ? "premium pending until acres are on file" : "priced on " + basisPhrase}${uPx.status === "rma" ? "" : ", price final after Sep 14"} &middot; both columns are per acre &middot; Clover Ag / Spartan Insurance Agency &middot; (585) 233-1066 &middot; ${new Date().toLocaleDateString()}</div>
      </section>`;
      })();
      // ⚠ THE QUOTE SHEET IS A SECTION LIKE ANY OTHER (Kevin, 2026-09-23). The Full
      // Strategy Report he asked for is five pages — coverage stack, compare to last
      // year, the SCO/ECO trigger chart, the FSA election, futures levels — and two
      // of those (the side-by-side and the trigger record) are BUILT HERE, inside the
      // quote path, because that is where the priced units and their prior-year chain
      // already exist. Rebuilding them for the report would be a second copy of the
      // compare page, and this file's own record says what happens to a rule defined
      // twice: it drifts.
      //
      // So the report asks for the same builder with `quoteSheet: false` and gets the
      // compare page alone. It is NOT a new document — it is the identical `cmpPage`
      // the Print Quote button emits, which is the whole point: the grower's report and
      // the agent's quote cannot show one farm two different comparisons.
      if (PC.quoteSheet === false) return cmpPage;
      return `
      <section class="page">
        <div class="brand"><img class="logo" src="${logoSrc}" alt="Clover Ag Risk Management" /><div class="bs">Risk Management &middot; Spartan Insurance Agency LLC</div></div>
        <div class="qt">${MPCI_WHEAT_YEAR} Wheat Coverage Estimate</div>
        <div class="farm">${esc(farmName)} &mdash; ${esc(capCty(r.county))} County, ${esc(FIPS_STATE[fips]||"")}${r._quoteOnly?' <span class="tag">not insured 2026 &middot; APH from history</span>':''}</div>
        <div class="meta">${polNum?`Policy ${esc(polNum)} &middot; `:""}${acresPending ? "Acres &mdash; Pending &middot; premium pending acreage" : acres.toFixed(0) + " ac" + basisNote} &middot; ${unitEntered ? `${uLbl[curUnit]||curUnit} unit &mdash; stated` : `${uLbl[curUnit]||curUnit} unit &mdash; quote basis${unitSrc && unitSrc.unit !== curUnit ? `, ${unitSrc.year || "prior year"} was ${uLbl[unitSrc.unit]||unitSrc.unit}` : ""}${curUnit === "EU" ? "; a basic or optional unit costs more" : ""}`} &middot; RP &middot; ${aphProvenance({ quoteOnly: r._quoteOnly, edited: !!(RA.edCalc && RA.edCalc.approved != null), fromAipDb: !!(RA.selBlend || RA.wtd), rolled: RA.rolled != null, priorYear: r._aphYear === MPCI_WHEAT_YEAR - 1 }).label} ${aph.toFixed(1)} bu${(() => { const n = aphBasisNote({ source: RA.source, blend: RA.blend, quoteYear: MPCI_WHEAT_YEAR }); return n ? ` (${esc(n)})` : ""; })()} &middot; ${esc(WHEAT_PRACTICE_NAMES[uPrac] || uPrac)}${wheatYieldContextHtml(cvPct, yieldBasis, ctyExpBu)} &middot; price $${price.toFixed(2)}/bu${
          isCertifiedOrganic(uPrac) ? (uPx.status === "rma" ? " (RMA organic election)" : " (organic election, entered)") : ""
        }${
          // ⚠ THE PRICE VOLATILITY FACTOR IS THE SINGLE BIGGEST LEVER ON THE PREMIUM AND
          // THIS SHEET USED TO HIDE IT. RMA has published no 2027 wheat PVF, so the figure
          // is an assumption we chose — and an agent holding this page beside the carrier's
          // own Coverage Analyzer, which states it three times, could not see the one input
          // that explains any difference between them. At 0.20 vs R&H's 0.27 that difference
          // was $4.12/ac on KAST FARMS, with nothing on the document to account for it.
          // Print what we assumed, beside the price it belongs to.
          ` &middot; vol ${(+vol).toFixed(2)}${uPx.volStatus === "rma" || uPx.volStatus === "released" ? "" : " est."}`
        }${rOpts.size ? ` &middot; options ${esc([...rOpts].join(", "))}` : ""}</div>
        <table>
          <thead>
            <tr>
              <th></th>
              ${hasPrior ? colHead("2026 Current", `${Math.round(curCl*100)}% &middot; ${curGuar} bu @ ${d2(priceEl)}${pctHead(cSco, cScoPct, cEco, cEcoPct)}`) : ""}
              ${colHead("2027 Same", `${Math.round(curCl*100)}% &middot; ${q?q.guarBu.toFixed(1):'—'} bu @ ${d2(price)}${pctHead(qs, cScoPct, qe, cEcoPct)}`)}
              ${/* ⚠ A DERIVED UNIT STRUCTURE IS NOT A CUSTOMER FACT. The engine falls back to
                     (acres >= 20 ? "EU" : "BU") when the policy states none, and REPORTS that as
                     `unitSource: "derived_from_acres" | "nominal"` — provenance this line threw
                     away, printing "Recommended 75% Enterprise" three pages after the same
                     document said "unit structure not on file · premium not quoted". The engine's
                     own comment records why it matters: the BU->EU flip moves the 75->80 cost 27%,
                     so the structure can change WHICH level is right, not merely its price.
                     `unitSrc`/`unitEntered` are the same guard the meta header above already uses. */
                colHead(rec && rec.agentSelected ? "2027 Selected" : "2027 Recommended", rec?`${Math.round(rec.cl*100)}% ${uLbl[rec.unit]}${unitAssumedForPremium ? " (assumed)" : ""} &middot; ${rq?rq.guarBu.toFixed(1):'—'} bu @ ${d2(price)}${pctHead(rs, rScoPct, re, rEcoPct)}`:"—", true)}
            </tr>
            <tr class="sub"><th>$/acre</th>${hasPrior?'<th>Prem</th><th class="cov">Cov</th>':''}<th>Prem</th><th class="cov">Cov</th><th>Prem</th><th class="cov">Cov</th></tr>
          </thead>
          <tbody>
            ${gRow("Production guarantee /ac", pgTxt(curAph, curCl, curGuarBu, chain26 && chain26.guarSource), pgTxt(aph, curCl, q && q.guarBu), rec ? pgTxt(aph, rec.cl, rq && rq.guarBu) : "&mdash;")}
            ${gRow("Revenue guarantee /ac", rgTxt(curGuarBu, priceEl, cur.mc), rgTxt(q && q.guarBu, price * (q && q.priceElectionPct ? q.priceElectionPct : 1), q && q.liabPerAc), rgTxt(rq && rq.guarBu, price, rq && rq.liabPerAc))}
            ${row("MPCI", cur.mp,cur.mc, unitPrem(q?q.netAcre:null),q?q.liabPerAc:null, unitPrem(rq?rq.netAcre:null),rq?rq.liabPerAc:null)}
            ${row("SCO", cur.sp,cur.sc, unitPrem(qs?qs.netAcre:null),qs?qs.covPerAc:null, unitPrem(rs?rs.netAcre:null),rs?rs.covPerAc:null)}
            ${row("ECO", cur.ep,cur.ec, unitPrem(qe?qe.netAcre:null),qe?qe.covPerAc:null, unitPrem(re?re.netAcre:null),re?re.covPerAc:null)}
            ${row("Total", totP(cur.mp,cur.sp,cur.ep),totP(cur.mc,cur.sc,cur.ec), totP(unitPrem(q&&q.netAcre),unitPrem(qs&&qs.netAcre),unitPrem(qe&&qe.netAcre)),totP(q&&q.liabPerAc,qs&&qs.covPerAc,qe&&qe.covPerAc), totP(unitPrem(rq&&rq.netAcre),unitPrem(rs&&rs.netAcre),unitPrem(re&&re.netAcre)),totP(rq&&rq.liabPerAc,rs&&rs.covPerAc,re&&re.covPerAc), true)}
            ${uPpf.base != null ? row(ppLbl, null, cur.mc!=null?cur.mc*prevPpf:null, null, q?q.ppLiabPerAc:null, null, rq?rq.ppLiabPerAc:null) : ""}
          </tbody>
        </table>
        ${(() => {
          // THE DATABASES BEHIND A MULTI-DATABASE GUARANTEE, so the grower can check it. Only
          // where the quote was built per database (RA.dbQuote); a single approved yield needs none.
          const pd = RA.dbQuote && q && q.perDatabase;
          if (!pd || pd.length < 2) return "";
          const wBy = RA.blend && RA.blend.basis === "planned" ? `the ${MPCI_WHEAT_YEAR} acres entered`
                    : RA.blend && RA.blend.basis === "prev" ? `${MPCI_WHEAT_YEAR - 1} acres` : "acres";
          // ⚠ AND IT IS THE BLOCK THAT RUNS THIS PAGE OFF THE SHEET. An unbounded list on
          // a fixed sheet, exactly like the trigger page below. MARQUART BROS LLC's WYOMING
          // county sums ELEVEN databases and printed 966px against a 960px letter sheet, so
          // the whole page was pushed onto a fresh sheet with a blank one in front of it.
          //
          // MEASURED on that page 2026-10-13, varying only this block:
          //   with the enumeration    966px     without it   916px   (so it costs 50px)
          //   4..7 entries            954px               <- all the same: ONE line fewer
          // i.e. ~12px per wrapped line and ~14px of its own chrome, on a page with 44px of
          // slack. Capping the ENTRY COUNT alone bought one line and left 6px — which is
          // why the budget is in LINES, with the entries packed to fill them.
          //
          // One line holds ~160 characters at 7.2pt in this column, measured the same way.
          const APH_ENUM_CHARS_PER_LINE = 160, APH_ENUM_LINES = 2;
          const lead = `${pd.length} APH databases, each at its own approved yield, rounded to the tenth as RMA does and weighted by ${wBy}:`;
          const items = pd.map(d => `${esc(d.k)} APH ${d.aph} &rarr; ${(+d.guarBu).toFixed(1)} bu (${(+d.acres).toFixed(1)} ac)`);
          // ⚠ THE REMAINDER IS NAMED, NEVER DROPPED. A list that silently stops reads as a
          // complete list, and this one exists so the grower can CHECK the guarantee — a
          // short list makes the arithmetic look wrong. The total count and the resulting
          // bu/ac are printed either way, so the figure that matters is never truncated.
          let used = lead.length, shown = [];
          for (const it of items) {
            if (shown.length && used + 3 + it.length > APH_ENUM_CHARS_PER_LINE * APH_ENUM_LINES - 24) break;
            used += 3 + it.length; shown.push(it);
          }
          const more = items.length - shown.length;
          // The RESULT goes straight after the entries, so the arithmetic still reads as
          // arithmetic; the remainder is a separate sentence after it. Run together, the
          // page printed "+7 more, listed in full on the production report = 37.5 bu/ac",
          // which reads as the note being one of the terms.
          return `<div style="font-size:7.2pt;color:#5c6f5c;margin:1px 0 1px">Production guarantee sums ${lead}
            ${shown.join(" &middot; ")}
            = <b>${(+q.guarBu).toFixed(1)} bu/ac</b> at ${Math.round(curCl * 100)}%.${more ? ` <i>The other ${more} are listed in full on the production report.</i>` : ""}</div>`;
        })()}
        ${PC.stacks && pBars.length > 1 ? _rptBarsHtml({ bars: pBars, guideAt: rec ? rec.cl : null, premScale: Math.max(0, ...pBars.map(b => b.prem || 0)) }) : ''}
        ${PC.stacks && rec && rq && !(pBars.length > 1) ? _rptStackHtml({ bandYear: MPCI_WHEAT_YEAR, cl: rec.cl, aph, price,
            mpci: { prem: unitPrem(rq.netAcre), cov: rq.liabPerAc },
            sco: rs ? { prem: unitPrem(rs.netAcre), cov: rs.covPerAc } : null,
            eco: re ? { prem: unitPrem(re.netAcre), cov: re.covPerAc } : null,
            arcplc: (arcByCounty && arcByCounty[(FIPS_STATE[fips]||"") + "|" + county]) || null }) : ""}
        ${PC.recPanel && rec && rq ? (() => {
          // ⚠ THE SUBSIDY CELL SITS IN A ROW OF THREE-LAYER TOTALS, SO IT SUMS THREE
          // LAYERS. It read rq.grossAcre - rq.netAcre -- MPCI alone -- beside a "you pay"
          // and a "coverage" that both add MPCI + SCO + ECO. Correct arithmetic over the
          // wrong SET, which is the mixed-ratio class: on Kast (Orleans, 80% RP EU + SCO +
          // ECO) it printed $51.57/ac against the row’s own $91.00, understating what RMA
          // pays by $39.43/ac -- $18,090 on his 458.8 acres -- because the endorsements are
          // subsidised at 80% and carry the larger share. The screen shows no subsidy figure
          // at all, so this was a number the grower was handed and the agent never saw.
          // A layer with no gross contributes NOTHING and makes the cell blank rather than
          // short: a partial subsidy total cannot be told from a correct one on the page.
          const subLayers = [rq, rs, re].filter(Boolean);
          const govAc = subLayers.every(o => o.grossAcre != null && o.netAcre != null)
            ? subLayers.reduce((t, o) => t + (o.grossAcre - o.netAcre), 0) : null;
          const covAc = (rq.liabPerAc || 0) + (rs ? rs.covPerAc : 0) + (re ? re.covPerAc : 0);
          const premAc = (rq.netAcre || 0) + (rs ? rs.netAcre : 0) + (re ? re.netAcre : 0);
          const kpi = (v, k) => `<td style="text-align:center;padding:1px 5px;border:0">`
            + `<div style="font-size:13.5pt;font-weight:800;color:#1f5c2e;line-height:1.05">${v}</div>`
            + `<div style="font-size:6.5pt;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#8aa08a;margin-top:1px">${k}</div></td>`;
          // The guarantee price, beside the bushels it multiplies — bu/ac x $/bu is the
          // coverage figure two cells to its left, so the banner now shows its own arithmetic.
          // It says which price it is: "final" once RMA has published the projected price for
          // this state+practice (pxFor -> status "rma"), "est." while we are still tracking the
          // board through discovery. Same signal the disclaimer line already reads, so the two
          // can never contradict each other on one page.
          // ⚠ "FINAL PRICE" IS RMA'S NAME FOR A DIFFERENT NUMBER. RMA sets a
          // PROJECTED price from the pre-planting discovery window and a HARVEST
          // price from the harvest window; a revenue policy settles against both.
          // Labelling the projected price "final" because RMA has announced it
          // invites a grower to read $7.54 as the price his claim will settle at,
          // which is not knowable until harvest discovery closes. What "final"
          // was trying to say — announced, no longer our running estimate — is
          // carried by naming RMA as the source instead.
          const pxKind = uPx.status === "rma" ? "RMA projected"
                       : uPx.status === "organic-manual" ? "entered" : "est. projected";
          const drv = [];
          if (rec.corr != null) drv.push(`county corr <b>${rec.corr.toFixed(2)}</b>`);
          if (rec.lossFreq != null) drv.push(`below 80% APH <b>${Math.round(rec.lossFreq*100)}%</b> of yrs`);
          if (premiumOk && rec.upCost != null) drv.push(`next level up <b>+${d2(rec.upCost)}/ac</b>`);
          return `<div class="rec">
            <div class="rt">${Math.round(rec.cl*100)}% ${uLbl[rec.unit]}${unitAssumedForPremium ? " (assumed)" : ""} RP${rec.sco?' + ' + areaCoverageLabel('SCO', rScoPct):''}${rec.eco?' + ' + areaCoverageLabel('ECO', rEcoPct):''}</div>
            <table style="width:100%;border-collapse:collapse;margin-top:3px"><tr>
              ${kpi(premiumOk ? d2(premAc) : "Pending", "you pay /ac")}
              ${kpi(d2(covAc), "coverage /ac")}
              ${kpi(premiumOk && govAc != null ? d2(govAc) : "&mdash;", "subsidy /ac")}
              ${kpi(premiumOk && premAc > 0 ? Math.round(covAc / premAc) + ":1" : "&mdash;", "cover per premium \$")}
              ${kpi(rq.guarBu.toFixed(1), "guarantee bu/ac")}
              ${kpi(d2(price), `${pxKind} price /bu`)}
            </tr></table>
            ${drv.length ? `<div style="margin-top:4px;padding-top:4px;border-top:1px solid #dbeadb;font-size:8pt;color:#5c6f5c;text-align:center">${drv.join(" &nbsp;&middot;&nbsp; ")}</div>` : ""}
          </div>`;
        })() : ""}
        <div class="disc">${(() => {
          // ⚠ A SHEET THAT APPLIES A SUBSIDY MUST SAY SO. Kevin, 2026-10-13: "Quote needs
          // to state BFR." Every premium on this page was computed with up to 15
          // percentage points of extra subsidy and the document said nothing — so neither
          // the grower signing it nor the AIP receiving it could check the basis, and an
          // agent holding two sheets could not tell which one assumed BFR.
          // It states the POINTS, the BFR crop year, and WHERE the status came from,
          // because "the AIP's own waiver code says so", "we inferred it from the
          // subsidy" and "the agent typed it" are three different claims.
          //
          // ⚠ IT RIDES THE EXISTING DISCLAIMER LINE RATHER THAN TAKING A BLOCK OF ITS
          // OWN, and that is a measurement, not a preference: as its own `.note` it added
          // ~60px and pushed the quote sheet from 920px back over the 960px letter sheet
          // — reintroducing, on the same day, the blank pages this document was just
          // fixed for. `check-screens --print` caught it immediately. Bold and first on
          // the line keeps it legible at no height.
          const note = BFR.bfrQuoteNote(bfrQ, {
            source: PC.bfrMode && PC.bfrMode !== "auto" ? "agent" : (bfrQ.bfrMethod || "derived"),
            policyNumber: pRow && pNumbers ? pNumbers[String(pRow.policy_id)] : null,
          });
          return note ? `<b style="color:#1f5c2e">${_rptEsc(note.text)}</b> &middot; ` : "";
        })()}${recOverrides ? "" : "Shows the engine’s recommendation &middot; "}Estimate, not a bind &middot; producer $/ac on RMA ${MPCI_WHEAT_YEAR} rates${uPx.status === "rma" ? "" : ", price final after Sep 14"}${uPx.status === "organic-manual" ? " &middot; organic election entered by hand" : ""} &middot; Clover Ag / Spartan Insurance Agency &middot; (585) 233-1066 &middot; office@cloverag.com &middot; ${new Date().toLocaleDateString()}</div>
      </section>${cmpPage}`;
    }).filter(Boolean).join("");
  };

  // ── ONE COUNTY, TWO SHEETS: its quote, then its own trigger record ──────────
  // The old document printed every quote and then every trigger block, so a
  // three-county farm handed a grower his Monroe quote on sheet 3 and the Monroe
  // evidence on sheet 5. Interleaved, each county reads as one story.
  // ⚠ ONE PRINTER FOR BOTH SURFACES (Kevin, 2026-09-21: the Full Strategy Report "should have
  // exactly what print quote shows now"). The report used to call `wheatQuotePages` directly and
  // build its trigger pages separately, so the two documents could differ in what they showed
  // for one farm — and the report's only ran when "Detailed appendix" was ticked. It calls THIS
  // now, so a change to the quote reaches both. `recSink` is threaded because the report's
  // synthesis pages read the recommendations back out; Print Quote passes none and is unchanged.
  const wheatQuoteAndTriggerPages = async (farmName, cov, phRows, pStates, pNumbers, basePrice, baseVol, logoSrc, arcByCounty, pYears, bands, recOverrides, printCfg, levelSink = null, recSink = null) => {
    const PC = wheatPrintCfg(printCfg);
    const units = wheatPrintUnits(cov, phRows, pStates, pYears, PC.aph);
    // ⚠ QUOTES FIRST, THEN THE TRIGGER PAGES. The recommendation is what sets the SCO
    // band the evidence page prices, so the levels have to exist before that page is
    // built. Building it first is what left Cripple Creek's Wyoming evidence headed
    // "75% MPCI" beside a quote recommending 80%.
    // The caller's sinks when it passed any, so the recommendation the report SYNTHESISES is the
    // one the quote page beside it printed — not a second run that could answer differently.
    const levelByCounty = levelSink || {};
    const quoteByCounty = {};
    for (const u of units) {
      const county = (u.county || "").toUpperCase();
      quoteByCounty[county] = wheatQuotePages(farmName, cov, phRows, pStates, pNumbers, basePrice, baseVol, logoSrc, arcByCounty, pYears, county, levelByCounty, recOverrides, recSink || undefined, PC);
    }
    // The evidence page is a SECTION like any other: switched off, it is not built at all
    // rather than built and dropped — the fetch behind it is a live query per state.
    const trig = PC.triggerPage ? await wheatTriggerPage(farmName, cov, phRows, pStates, pYears, bands, logoSrc, true, levelByCounty, PC.aph) : null;
    let out = "";
    for (const u of units) {
      const county = (u.county || "").toUpperCase();
      if (quoteByCounty[county]) out += quoteByCounty[county];
      if (trig && trig[county]) out += trig[county];
    }
    return out;
  };

  // Assembles the trigger-record page: the SAME units the quote pages walk, plus one
  // read of the county revenue history behind them. Returns "" when there is nothing
  // to print — no band ticked, no quotable unit, or no settled county revenue loaded —
  // so every caller can concatenate it blindly and a farm outside the loaded counties
  // simply prints the quote it always did.
  // `levelByCounty` — { COUNTY: recommended coverage level }, produced by
  // wheatQuotePages. The SCO band is measured from the base level, so the evidence page
  // has to be computed at the level the quote beside it recommends or the two describe
  // different products. Absent (a caller that prints no quote), it falls back to the
  // grower's own election and the page says which level it used, as it always has.
  // `aphCtx` — the same { units, agent } the quote pages take, so this page measures the SCO
  // band on the APH the quote beside it priced. It read `r.aph_yield` until 2026-09-18.
  const wheatTriggerPage = async (farmName, cov, phRows, pStates, pYears, bands, logoSrc, perCounty, levelByCounty, aphCtx) => {
    if (!bands || !(bands.sco || bands.eco)) return perCounty ? {} : "";
    const blocks = wheatPrintUnits(cov, phRows, pStates, pYears, aphCtx).map(r => {
      const county = (r.county || "").toUpperCase();
      if (!county || !(r._aph && r._aph.aph > 0)) return null;
      const prac = practiceCodeOf(r);
      return {
        st: (pStates[String(r.policy_id)] || "NY").toUpperCase(), county,
        label: capCty(county) + " County" + (prac === "003" ? "" : " · " + (WHEAT_PRACTICE_NAMES[prac] || prac)),
        aph: +r._aph.aph,
        baseCl: (levelByCounty && levelByCounty[county] != null) ? +levelByCounty[county]
              : (r.coverage_level != null ? +r.coverage_level : 0.75),
      };
    }).filter(Boolean);
    if (!blocks.length) return perCounty ? {} : "";
    // Filtered by STATE, then matched to counties in JS. A county=in.() list would have
    // to quote and encode multi-word county names (ST LAWRENCE) to survive PostgREST;
    // a state's whole wheat history is ~280 rows, so there is nothing to save by it.
    const sts = [...new Set(blocks.map(b => b.st))].filter(Boolean);
    if (!sts.length) return perCounty ? {} : "";
    const rows = await sbFetch("sco_eco_county", {
      filters: ["crop=eq.WHEAT", `state=in.(${sts.join(",")})`],
      select: "crop_year,state,county,revenue_ratio,projected_price",
    }).catch(() => []);
    if (!rows || !rows.length) return perCounty ? {} : "";
    return wheatTriggerPageHtml(farmName, blocks.map(b => ({
      ...b, rows: rows.filter(x => (x.state || "").toUpperCase() === b.st && (x.county || "").toUpperCase() === b.county),
    })), bands, logoSrc, perCounty);
  };

  const WHEAT_PRINT_CSS = `
    /* 0.4in, not 0.5in: measured over the whole CY2027 wheat book (436 pages, 92
       farms) exactly TWO pages exceeded a 0.5in sheet, both by 5px. At 0.4in the
       sheet is 739 x 979 CSS px and every page in the book fits with 14px to spare,
       with nothing trimmed and no figure truncated. Three other print surfaces here
       already use 0.4in. check-screens --print READS this rule rather than assuming
       it, so the page setup and the measurement of it cannot drift apart. */
    @page { size: letter; margin: 0.4in; }
    /* Browsers drop background colours when printing unless this is set. Without it every
       shaded cell on this quote — the header band, the recommendation panel, the totals row,
       the "not insured" tag — printed as plain white, so the page a grower is handed lost
       the distinction between what he pays today and what we are recommending. */
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color:#1a2e1a; margin:0; }
    /* ⚠ ONE COUNTY, ONE SHEET. Every page here is built to fit letter portrait,
       and break-inside:avoid is what turns a page that does not fit into a
       visible layout bug rather than a silent orphan — the printed quote used to
       spill its disclaimer alone onto a second sheet, six pages for three
       counties with two of them nearly blank. */
    .page { page-break-after: always; break-inside: avoid; page-break-inside: avoid; padding:4px 0; }
    .page:last-child { page-break-after: auto; }
    .brand { border-bottom:2px solid #2d6a2d; padding-bottom:5px; margin-bottom:7px; display:flex; align-items:flex-end; gap:10px; }
    .logo { height:38px; width:auto; display:block; }
    .bs { font-size:7.5pt; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color:#60735f; padding-bottom:3px; }
    .qt { font-size:8pt; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:#4a9a4a; margin-top:6px; }
    .farm { font-size:16pt; font-weight:800; margin:1px 0 2px; }
    .tag { font-size:8pt; font-weight:700; color:#8a6d1f; background:#fbf3dc; border:1px solid #ecdca6; border-radius:4px; padding:1px 6px; vertical-align:middle; text-transform:uppercase; }
    .meta { font-size:9pt; color:#5c6f5c; margin-bottom:8px; }
    table { width:100%; border-collapse:collapse; font-size:10.5pt; }
    th, td { padding:4.5px 9px; text-align:right; }
    thead th { background:#f0f6f0; font-size:8.5pt; font-weight:800; text-transform:uppercase; letter-spacing:.04em; color:#4a7a4a; border-bottom:1.5px solid #c8d8c8; }
    thead th.rec-h { background:#e7f3e7; color:#1f5c2e; }
    .colsub { font-size:7.5pt; font-weight:600; color:#8aa08a; text-transform:none; letter-spacing:0; margin-top:3px; }
    tr.sub th { background:#f7faf7; font-size:8pt; padding:3px 9px; }
    th.cov, td.cov { color:#60735f; border-right:1px solid #eef3ee; }
    td { border-bottom:1px solid #eef3ee; }
    /* ⚠ THE SIDE-BY-SIDE IS THE LONGEST TABLE THIS CSS DRAWS, AND IT SPILLED.
       Measured at letter in PRINT media (7.5 x 10in = 720 x 960px): 1,009px, so the
       disclaimer and the last note orphaned onto a second sheet. That was invisible
       while the page lived in the optional appendix; it is page TWO of the customer
       report now (Kevin, 2026-09-23), and a 5-page report that prints 6 is the thing
       he has asked twice to be fixed.
       It buys the room from LEADING, not from content, and not one sourced sentence
       comes off the page. Scoped to .cmp (NO BACKTICKS — this whole block is inside a
       JS template literal, and a backtick here terminates it: the build still compiled
       and the app rendered a login screen, because the CSS string became fragments and
       the component that owns it stopped mounting) so the quote sheet, the trigger
       record and
       the futures page — which have room — are byte-identical.
       Measured on WOODLAWN's page, which carries three of the notes:
         4.5px (shared) 1,009px   3.25px 962px   3px 952px   2.75px 943px   2.5px 933px
       2.5px is the setting, for the 27px of headroom rather than for the look: a farm
       whose page carries a fourth note — the prevent-plant buy-up withdrawal and the
       guarantee-method note can both appear — is taller than this one, and a value
       chosen to just clear Woodlawn would spill on the next farm along. */
    .cmp th, .cmp td { padding:2.5px 9px; }
    .cmp tr.sub th { padding:2px 9px; }
    td.lbl { text-align:left; font-weight:700; color:#3a4a3a; }
    td.c { text-align:center; color:#3a4a3a; font-weight:600; }
    tr.tot td { font-weight:800; background:#f4f8f4; border-top:2px solid #c8d8c8; color:#1f5c2e; }
    .rec { margin-top:10px; border:1.5px solid #b7d3b7; background:#f4faf4; border-radius:8px; padding:7px 10px; page-break-inside:avoid; }
    .rt { font-size:11pt; font-weight:800; color:#1f5c2e; text-align:center; letter-spacing:.01em; }
    .rec ul { margin:4px 0 0; padding-left:16px; font-size:9.5pt; color:#3a4a3a; line-height:1.45; }
    .rec table td { border:0; }
    .disc { margin-top:9px; font-size:7.5pt; color:#a3b0a3; line-height:1.35; border-top:1px solid #eef3ee; padding-top:5px; }
    /* ── THE SHEET HAS TO FIT, AND TUNING IT ON ONE FARM IS WHY IT KEEPS NOT FITTING ──
       The .cmp note above measured Woodlawn's page and said so plainly: "a value chosen
       to just clear Woodlawn would spill on the next farm along." HOWLETT FARMS is the
       next farm along. Measured 2026-10-13 with check-screens --print, his two-county
       quote printed SIX .page blocks of which FOUR were over a letter sheet — 1036,
       1026, 1036, 1010 against 960 — and because .page carries break-inside:avoid the
       browser pushed each whole block to the next sheet and left a BLANK one in front
       of it. That is the "several blank pages" on a document a grower is handed.
       These rules buy the room back from LEADING AND PADDING, never from content: not
       one sourced sentence, figure or note comes off the page.
       ⚠ !important IS LOAD-BEARING HERE, NOT LAZINESS. The note blocks are emitted
       with INLINE styles from several builders, and an inline style beats a class
       rule. The alternative is editing the same padding in four string templates and
       hoping the fifth is found.
       ⚠ AND DO NOT RE-TUNE THESE BY EYE. check-screens --print prints every page's
       height against the sheet; that is the only thing that has ever caught this, and
       it is why 250 -> 196 -> 168 happened three times.
       ⚠ NO BACKTICKS IN THIS COMMENT. The block above says so and I wrote two anyway,
       naming the very command that measures this — the build failed instantly, which
       is the one mercy: a backtick here ends the template literal. */
    .note { padding:5px 8px !important; margin-top:5px !important; line-height:1.3 !important; font-size:7.6pt !important; }
    .cmp .disc { margin-top:5px !important; line-height:1.25 !important; }
    .page { padding:0 !important; }
    th.cov, td.cov { padding-top:1px !important; padding-bottom:1px !important; }
    /* ⚠ THE EARLIER TRIM WAS SCOPED TO .cmp AND THE QUOTE SHEET IS NOT .cmp. Its note
       reasoned that "the quote sheet, the trigger record and the futures page — which
       have room — are byte-identical", and that premise is simply out of date: measured
       2026-10-13 the QUOTE sheet was the worst page in the document at 1,036px. Scoping
       the fix to the page that was measured is the same mistake as tuning to the farm
       that was measured. These apply to every page; .cmp keeps its own tighter value.
       After this, Howlett's six pages measure 936 / 833 / 519 / 936 / 804 / 519 — the
       worst with 24px of headroom rather than 76px of overflow. */
    th, td { padding-top:2.5px !important; padding-bottom:2.5px !important; }
    table { line-height:1.2 !important; }
    .rec { margin-top:6px !important; padding:5px 9px !important; }
    .qt.sm { margin-top:9px !important; }
    .mut { font-size:8pt; font-weight:600; color:#677167; }
    td.lbl2, th.lbl2 { text-align:left; color:#5c6f5c; font-weight:600; }
    td.pick { text-align:center; font-weight:800; } td.pick.p { color:#1f5c2e; } td.pick.a { color:#8a5a00; }
    .qt.sm { margin-top:16px; }
    tr.past td { color:#677167; }
    .no-print { text-align:center; padding:10px; }
    /* ── PAGE ONE OF THE STRATEGY REPORT ──────────────────────────────────
       Its own classes, because it is the one page made of SENTENCES rather
       than figures. It used to borrow .qt (the uppercase 8pt letter-spaced
       eyebrow label) for its table and inherit the sheet-wide right-aligned td, so
       the whole recommendation printed as right-aligned all-caps.
       Nothing below is shared with the quote pages. */
    .sp-h1 { font-size:15pt; font-weight:800; color:#1a2e1a; letter-spacing:0; }
    .sp-sub { font-size:9pt; color:#6b7c6b; margin-top:1px; }
    .sp-sec { font-size:8pt; font-weight:800; letter-spacing:.12em; text-transform:uppercase;
            color:#4a9a4a; margin:13px 0 5px; }
    /* the recommendation block: label left, answer right, both readable */
    table.sp { width:100%; border-collapse:collapse; margin:0; }
    table.sp td { text-align:left; padding:6px 10px; font-size:10pt; vertical-align:top;
                border-bottom:1px solid #eef3ee; text-transform:none; letter-spacing:0; }
    table.sp td.k { width:132px; font-weight:700; color:#5c6f5c; font-size:8.5pt;
                 text-transform:uppercase; letter-spacing:.06em; padding-top:8px; }
    table.sp td.v { color:#1a2e1a; font-weight:600; }
    table.sp td.v .amt { color:#1f5c2e; font-weight:800; }
    table.sp td.v .mut { color:#60735f; font-weight:500; font-size:9pt; }
    /* prose blocks — sentence case, left aligned, room to breathe */
    .sp-p { font-size:9.5pt; line-height:1.5; color:#2b3b2b; margin:3px 0 0; text-transform:none;
           letter-spacing:0; text-align:left; }
    .sp-need { border:1px solid #e8d9a8; background:#fdf9ec; border-radius:6px; padding:8px 11px; }
    .sp-need li { font-size:9.5pt; line-height:1.5; color:#6b5a1f; margin-bottom:2px; }
    .sp-need ul { margin:2px 0 0; padding-left:16px; }
    table.spx { width:100%; border-collapse:collapse; margin-top:2px; }
    table.spx td { text-align:left; padding:4px 10px; font-size:9pt; border-bottom:1px solid #f0f4f0;
                text-transform:none; letter-spacing:0; }
    table.spx td.k { font-weight:700; color:#3a4a3a; width:120px; }
    table.spx td.t { color:#60735f; }
    .sp-foot { font-size:8pt; color:#60735f; margin-top:7px; line-height:1.45; }
    @media print { .no-print { display:none; } }`;

  // ⚠ A PRINT WINDOW NEEDS A WAY OUT, AND ON A PHONE IT IS THE ONLY WAY OUT.
  // Kevin, 2026-09-23: *"once I select FSR, it opens to print but I have no way of
  // getting back to Silas from there when I'm on mobile"*. On a desktop the print
  // document is a tab you close; on iOS and Android a script-opened tab presents no
  // visible back affordance at all, so the agent is stranded on the document mid-meeting
  // with no route back to the app.
  //
  // The way out is the shared bar every print window carries (src/printBackBar.js):
  // close the script-opened tab, and where a mobile browser refuses, NAVIGATE back to
  // the app — a button that silently does nothing is worse than no button.
  const openWheatPrintDoc = (title, pages) => {
    if (!pages) { alert("No wheat units to print (selected farms may have no wheat coverage/history)."); return; }
    const w = window.open("", "_blank"); if (!w) { alert("Pop-up blocked — allow pop-ups to print."); return; }
    writePrintWindow(w, `<!doctype html><html><head><meta charset="utf-8"><title>${String(title).replace(/[<>]/g,"")}</title><style>${WHEAT_PRINT_CSS}</style></head><body>`
      + pages
      + `<script>window.onload=function(){setTimeout(function(){try{window.print()}catch(e){}},400)}<\/script></body></html>`);
  };

  // HARD GUARD: never emit a farmer-facing document containing an MPCI premium we
  // can't reconcile to the AIP. Our MPCI runs LIGHT in unvalidated counties, and an
  // understated premium is the direction that gets an agency in trouble. This backs
  // the disabled buttons so the block can't be bypassed. See tools/mpci-quoter/validate_nau.mjs.
  // The engine follows RMA's P11-1 spec and is exact to the cent against real AIP quotes,
  // including above the top of the rate table (tools/mpci-quoter/validate_nau.mjs, BUG-095).
  // Above that top level RMA prints TWO rate-differential formulas (P11-1 §14): a cubic
  // surcharge when the APH database's option list names QL, YE or YC, none when TA is
  // elected alone. With the database's list (`yield_options`, P15 f[38]) the quote is exact.
  // ADVISORY, NOT A BLOCK — for the one case left: a database above the top level whose list
  // the AIP's file does not state. The engine then prices the higher formula and carries the
  // other beside it (rdfBranchAlt), so the note names the counties rather than refusing a
  // grower a quote. (This used to blame the revenue add-on. It never was the add-on.)
  const wheatHighTaCounties = (covRows, pStates) => {
    const out = new Set();
    (covRows || []).filter(c => c.crop === "WHEAT" && !isEndorsement(c.plan) && c.aph_yield != null).forEach(c => {
      const county = (c.county || "").toUpperCase();
      const fips = stFips((pStates[String(c.policy_id)] || "NY").toUpperCase());
      if (!county || !wheatCountyInSet(fips, county)) return;
      // Effective index uses the ADJUSTED yield (§13) — prefer the AIP's, since a YA grower's
      // adjusted yield can't be derived from the APH database.
      const pc = practiceCodeOf(c);
      const ser = yieldSeries("WHEAT", county, pc).map(o => ({ year:o.yr, yield:o.y }));
      const rc = ser.length ? computeApprovedYield2027({ state:fips, county, practice:pc, yields:ser, ta:false, ye:false }) : null;
      const derived = (rc && rc.approved != null) ? Math.min(rc.approved, +c.aph_yield) : +c.aph_yield;
      const adj = (c.adjusted_yield != null && +c.adjusted_yield > 0) ? +c.adjusted_yield
                : (c.rate_yield != null && +c.rate_yield > 0) ? +c.rate_yield : derived;
      const cl = c.coverage_level != null ? +c.coverage_level : 0.75;
      // Flag only where the formula is genuinely UNDETERMINED: above the top level (0.85 on
      // every wheat offer) AND the database list does not settle it. A known list is exact.
      const undetermined = rdfSurchargeBranch({ yieldOptions: c.yield_options ?? null, electedOptions: c.insurance_options ?? null }) === "unknown";
      if (undetermined && Math.round(cl * (+c.aph_yield) / adj * 100) / 100 > 0.85) out.add(capCty(county));
    });
    return [...out];
  };

  // Single-farm print (current farm's loaded data).
  const printWheatQuote = async () => {
    const logo = await loadLogoDataUri() || `${window.location.origin}/Clover_Ag_Logo1_transparent.png`;
    // Each county's quote is followed by its OWN trigger record — the evidence
    // behind the SCO/ECO lines that county is being asked to buy. Prints whichever
    // bands are ticked on the History tab, so an SCO-only conversation hands over
    // an SCO-only page.
    // What the agent is looking at. Printing the engine's answer while the screen shows
    // an edited one is the disagreement this argument exists to close.
    const pages = await wheatQuoteAndTriggerPages(farmer.name, cov, ph, polStates, polNumbers, +px.price, +px.vol, logo, null, polYears, btBands, recCfg, { ...printSec, barSel, aph: loadedAphCtx(), unitEntries: unitEntry, bfrMode });
    openWheatPrintDoc(`${farmer.name} — 2027 Wheat Quote`, pages);
  };

  // Full 3-page strategy package: ARC/PLC election rec · coverage quote · marketing plan.
  // Self-contained — fetches ARC/PLC (arc_plc_reference + arc_co_county for the farm's
  // wheat counties) and marketing (futures) fresh; reuses the wheat quote pages.
  const printFullStrategyReport = async () => {
    if (!farmer) return;
    setReportBusy(true);
    try {
      const logo = await loadLogoDataUri() || `${window.location.origin}/Clover_Ag_Logo1_transparent.png`;
      // Page 1 — ARC/PLC per wheat county
      const wheatBase = (cov || []).filter(c => c.crop === "WHEAT" && c.aph_yield != null && !isEndorsement(c.plan));
      // THE FULL STRATEGY REPORT IS A PRINTED DOCUMENT AND IT PRICES CY2027 RATES,
      // so it needs the same crop-year scoping the card and the quote pages have.
      // `wheatBase` carries BOTH crop years and `.find()` below took whichever
      // arrived first, so a CY2026 row could supply the acreage a CY2027
      // recommendation was priced on - the first-wins class this repo already
      // records for type_code, unit_number and the P15 yield key.
      const fsrYr = (rr) => { const y = polYears ? polYears[String(rr && rr.policy_id)] : null; return y == null ? null : +y; };
      const fsrGrp = new Map();
      for (const c of wheatBase) {
        const k = (c.county || "").toUpperCase() + "|" + (c.practice_code || "003");
        const g = fsrGrp.get(k) || { cur: null, prev: null };
        const y = fsrYr(c);
        if (y === MPCI_WHEAT_YEAR) { if (!g.cur) g.cur = c; }
        else if (y === MPCI_WHEAT_YEAR - 1) { if (!g.prev) g.prev = c; }
        else if (!g.cur) g.cur = c;
        fsrGrp.set(k, g);
      }
      const csMap = new Map();
      wheatBase.forEach(c => { const county = (c.county || "").toUpperCase(); const state = (polStates[String(c.policy_id)] || "NY").toUpperCase(); if (county) csMap.set(state + "|" + county, { county, state }); });
      const countyStates = [...csMap.values()];
      const states = [...new Set(countyStates.map(x => x.state))];
      let arcPage = "", arcByCounty = {}, arcRowsOut = [], arcProgramYear = null, arcBasisOut = null, arcPendingMsg = null, arcSheetNote = null;
      // THE ONE ARC/PLC PROGRAM-YEAR CONTEXT (cropYear.js electionYearContext) — the same rule and the
      // same session pick the Coverage Advisor's picker uses, so this report can never print a program
      // year the Advisor is not on. PENDING means FSA has not published the active year: the report
      // says so and computes nothing from another year.
      const arcPublished = countyStates.length && states.length
        ? availableCropYears(await sbFetch("arc_plc_reference", { select: "program_year" }).catch(() => []), "program_year") : [];
      const arcCtxR = electionYearContext({ published: arcPublished, windows: ELECTION_WINDOWS, storedYear: readStoredYear("arcPlcElection", null) });
      const arcYr = arcCtxR.pending ? null : arcCtxR.year;
      if (arcCtxR.pending && countyStates.length && states.length) {
        arcProgramYear = arcCtxR.year;
        arcPendingMsg = arcPlcPendingMessage(arcCtxR.year);
        arcPage = caArcPlcPendingPageHtml(farmer.name, "Wheat", logo, arcPendingMsg);
      }
      if (countyStates.length && states.length && arcYr != null) {
        const [refRows, ctyRows] = await Promise.all([
          sbFetch("arc_plc_reference", { filters: [`program_year=eq.${arcYr}`, "crop=eq.WHEAT"] }).catch(() => []),
          sbFetch("arc_co_county", { select: "program_year,crop,state,county,practice,benchmark_yield,benchmark_revenue,guarantee_revenue,actual_yield,actual_revenue,payment_rate", filters: [`program_year=eq.${arcYr}`, "crop=eq.WHEAT", `state=in.(${states.join(",")})`] }).catch(() => []),
        ]);
        const ref26 = (refRows || []).find(r => r.crop === "WHEAT") || (refRows || [])[0];
        // caCountyKey on BOTH sides: arc_co_county writes "ST. LAWRENCE" while the
        // book writes "ST LAWRENCE", and the miss is silent — `.filter(Boolean)`
        // two lines down drops the county off the printed ARC/PLC page entirely.
        //
        // ⚠ EVERY PRACTICE ROW IS KEPT (#200). FSA publishes no ALL row for a county it splits
        // by irrigation, and keeping whichever arrived last priced a split county on one side
        // of it. resolvePracticeRow decides — and this report holds no farm's irrigated share,
        // so a split county REFUSES and says why rather than picking.
        const ctyMap = {}; (ctyRows || []).forEach(r => { const k = (r.state || "").toUpperCase() + "|" + caCountyKey(r.county); (ctyMap[k] = ctyMap[k] || {})[practiceKeyOf(r)] = r; });
        // ⚠ ONE ENGINE, AND NO PAYMENT YIELD (work order E, 2026-09-18). This priced PLC at a
        // hard-coded 60 bu and named a winner off it. countyRead is the call the Coverage
        // Advisor and the one-page election sheet make; without the farm's payment yield it
        // returns the BREAKEVEN — "PLC above N bu, ARC-CO below" — which is the answer.
        const arcRows = countyStates.map(cs => {
          const res = resolvePracticeRow(ctyMap[cs.state + "|" + caCountyKey(cs.county)], null);
          if (!res.row) return res.split ? { ...cs, refused: res.refused } : null;
          const rd = countyRead({ countyRow: res.row, ref: ref26 });
          // #208's election outlook reads the county's own published benchmark and guarantee and
          // the effective reference price — CARRIED from the FSA rows, never recomputed.
          return rd.usable ? { ...cs, read: rd, label: electionLabel(rd),
            benchmarkYield: res.row.benchmark_yield != null ? +res.row.benchmark_yield : null,
            benchmarkRevenue: res.row.benchmark_revenue != null ? +res.row.benchmark_revenue : null,
            guaranteeRevenue: res.row.guarantee_revenue != null ? +res.row.guarantee_revenue : null,
            effectiveRefPrice: ref26.effective_ref_price != null ? +ref26.effective_ref_price : null } : null;
        }).filter(Boolean);
        // The farm election sheet itself — every covered commodity, not just the wheat this report
        // is otherwise about — through the SAME loader the Coverage Advisor's button uses.
        // ⚠ farmIdsOf, NEVER farmer.id — this panel's farm is grouped by name and has no id.
        // And the failure is REPORTED: a swallowed error here prints a report with no election
        // sheet, which reads as a feature that was never built rather than as a job that failed.
        try {
          const sheet = await loadFarmElectionSummary(sbFetch, { farmerIds: farmIdsOf(farmer), farmerName: farmer.name, programYear: arcYr });
          arcPage = (sheet.crops || []).length ? farmElectionSheetSection(sheet) : "";
          if (!arcPage) arcSheetNote = "no covered commodity on this farm has " + arcYr + " FSA figures";
        } catch (e) { arcPage = ""; arcSheetNote = (e && e.message) || String(e); }
        arcRowsOut = arcRows; arcProgramYear = ref26 && ref26.program_year != null ? +ref26.program_year : null;
        // What the estimates REST ON, for the report to say: the marketing-year price (USDA's
        // forecast until final) and whether FSA's 2026 county yields are out — ARC-CO uses each
        // county's historical yield in their place until they are. The tie band is the rule's own.
        arcBasisOut = ref26 ? { price: ref26.mya_price != null ? +ref26.mya_price : null, status: ref26.mya_status || null,
          refPrice: ref26.effective_ref_price != null ? +ref26.effective_ref_price : null,
          loanRate: ref26.national_loan_rate != null ? +ref26.national_loan_rate : null,
          countyYieldsFinal: arcRows.every(a => !a.read || a.read.yieldBasis === "published"),
          tieBand: PICK_TIEBREAK } : null;
        // Same per-county reads feed the coverage-stack companion on page 2 — ARC/PLC is a
        // base-acre payment, so it sits BESIDE the stack, never as one of its bands.
        arcRows.forEach(a => { if (a.read) arcByCounty[a.state + "|" + a.county] = { label: a.label, state: a.read.state,
          arcAc: a.read.arcPerAcre, plcRate: a.read.plcRate, breakeven: a.read.breakeven }; });
      }
      // Page 2 — coverage quote (existing per-county pages) + coverage stack w/ ARC/PLC companion
      const stratLevels = {}, stratRecs = {};
      const stratFacts = {};
      // ⚠ THE SAME CALL THE PRINT QUOTE BUTTON MAKES, so the two documents cannot show one farm
      // different coverage. It interleaves each county's trigger record exactly as Print Quote
      // does; the sinks are passed so the synthesis pages read back the recommendation that was
      // actually printed. `btBands` is the History tab's own band selection, as Print Quote uses.
      // `unitEntries` rides in the options object (BUG-088 follow-up): a unit structure the AGENT
      // stated must reach this printer too, or the report withholds a premium the quote prices.
      const quotePages = await wheatQuoteAndTriggerPages(farmer.name, cov, ph, polStates, polNumbers, +px.price, +px.vol, logo,
        arcByCounty, polYears, btBands, recCfg,
        { ...printSec, barSel, unitEntries: unitEntry, factSink: stratFacts, aipAcreage: aipAcre, aph: loadedAphCtx(), bfrMode },
        stratLevels, stratRecs);
      // ── PAGES 2 AND 3 OF THE REPORT (Kevin, 2026-09-23) ─────────────────────
      // "1.) Coverage Stack 2.) Compare to last year 3.) SCO/ESO trigger chart
      //  4.) The clear concise FSA election recommendation 5.) Futures level
      //  technical targets."
      //
      // Both are the SAME builders Print Quote uses, asked for without the quote
      // sheet — `quoteSheet: false` returns the compare page alone, and
      // `triggerPage` interleaves each county's record after it. They were built
      // already and went only into the optional appendix, which is off by default,
      // so a grower's report had never carried either.
      //
      // ⚠ THE SECTION TOGGLES ARE OVERRIDDEN, NOT READ. `printSec` is the agent's
      // choice for the QUOTE document; the report's five pages are the shape Kevin
      // asked for, and a chip left unticked on the quote must not silently delete a
      // page from the grower's report. `lastYear` is forced on for the same reason
      // the chip row warns about it — without it the compare page cannot build.
      const cmpTrigPages = await wheatQuoteAndTriggerPages(farmer.name, cov, ph, polStates, polNumbers, +px.price, +px.vol, logo,
        arcByCounty, polYears, btBands, recCfg,
        { ...printSec, quoteSheet: false, lastYear: true, comparePage: true, triggerPage: true,
          stacks: false, recPanel: false,
          barSel, unitEntries: unitEntry, aipAcreage: aipAcre, aph: loadedAphCtx(), bfrMode });
      // Page 3 — marketing (2027 wheat, the quoted crop year). Honor the grower's delivery
      // plan (deliver at harvest vs store) so the %-sold schedule doesn't push post-harvest
      // sales onto someone who empties the bins at harvest.
      let wheatSM = null;
      const fids = farmIdsOf(farmer);
      if (fids.length) {
        const pl = await sbFetch("grain_crop_plan", { filters: [`farmer_id=in.(${fids.join(",")})`, "crop_year=eq.2027", "crop=eq.Wheat"] }).catch(() => []);
        wheatSM = (pl || []).map(r => r.storage_mode).find(Boolean) || null;
      }
      const mkt = await grainReportMarketing("Wheat", 2027, wheatSM);
      const mktPage = mkt ? grainMarketingPageHtml(farmer.name, "Wheat", 2027, mkt, logo) : "";
      // ── CASH WHEAT TARGETS (Kevin, 2026-09-21) ────────────────────────────────────────────
      // GENERIC, and deliberately nothing to do with this farm's working orders: the question is
      // "where should I set targets for wheat?" The rungs are `mkt.targets` — already measured by
      // grainTargets from this board's own five years — translated to cash at the basis the
      // elevator POSTED for that delivery month. A board with no posted basis prints board levels
      // and says they are board levels; it never converts at a basis nobody posted.
      let cashTargets = "";
      if (mkt && mkt.targets && (mkt.targets.rungs || []).length) {
        const bb = await sbFetch("grain_basis_board", { select: "commodity,location,term,cme_contract,ship_month,basis,sheet_version,sheet_updated" }).catch(() => []);
        const sheet = (bb || []).find(r => r.sheet_version) || {};
        const ct = wheatCashTargets({
          rungs: mkt.targets.rungs, basisRows: bb || [], contractSym: mkt.contract && mkt.contract.sym,
          contractLabel: mkt.contract ? `${mkt.contract.sym} (${_rptMON[mkt.contract.mo]} '${String(mkt.contract.yr).slice(2)})` : null,
          boardLast: mkt.tc && mkt.tc.last, asOf: mkt.asOf || null,
          sheetVersion: sheet.sheet_version || null, sheetUpdated: sheet.sheet_updated || null,
        });
        cashTargets = cashTargetsSection(ct, { farmName: farmer.name });
      }
      // Trigger record sits with the coverage it belongs to — after the quote, before
      // marketing — so the package reads election → coverage → evidence → marketing.
      // Built only for the appendix: it is a live query per state, and the standard
      // report carries its own summary of the same record.
      // ⚠ NO SECOND TRIGGER PAGE. wheatQuoteAndTriggerPages already interleaves each county's
      // trigger record with its quote, the way Print Quote does; building it again here printed
      // the same evidence twice, once beside its county and once in a block at the back.
      const trigPage = "";

      // ── PAGE ONE: the canonical strategy object ────────────────────────────
      // One object per (farm, crop, crop year), assembled from what each ENGINE
      // said. This block does no arithmetic and makes no recommendation — it
      // hands the four domains to buildFarmStrategy, which reconciles them,
      // reports disagreement and returns the row page one prints. A domain that
      // was not supplied stays null and is NAMED, never filled in.
      const strategies = [];
      for (const cs of countyStates) {
        const arc = arcByCounty[cs.state + "|" + cs.county] || null;
        // The renewal-year row where one exists; the prior year only as reference.
        const uG = fsrGrp.get(cs.county + "|003") ||
                   [...fsrGrp.entries()].filter(([k]) => k.split("|")[0] === cs.county).map(([, v]) => v)[0] || null;
        const uCov = uG ? (uG.cur || uG.prev) : null;
        const uCurAc = (uG && uG.cur && +uG.cur.acres > 0) ? +uG.cur.acres : 0;
        const uPriorAc = (uG && uG.prev && +uG.prev.acres > 0) ? +uG.prev.acres : 0;
        const series = uCov ? yieldSeries("WHEAT", cs.county, uCov.practice) : [];
        // ⚠ THE QUOTE PAGE'S OWN ANSWER, NEVER A SECOND ONE. This used to call
        // recommendWheat again with a DIFFERENT argument list — no `incumbent`,
        // no `cv`, and the quoted year's acres where the quote page uses the
        // acreage it resolved — so page 1 and page 2 of the same document
        // recommended different coverage levels. The fallback below is kept only
        // for a county the quote page produced no page for; it can no longer
        // CONTRADICT a printed quote, because where a quote exists it wins.
        const uRec = stratRecs[cs.county] || (uCov ? recommendWheat(FIPS_OF_STATE[cs.state] || "36", cs.county,
          uCov.aph_yield, uCurAc, +px.price, +px.vol, null, series, cs.state,
          uCov.rate_yield, uCov.adjusted_yield, uCov.practice_code || "003",
          // Prior-year acreage is REFERENCE. The engine withholds when the quoted
          // year reports none, rather than pricing on land nobody reported for it.
          { ppAcres: uCov.pp_acres, insuredAcres: uCurAc || null, unitStructure: uCov.unit_structure,
            priorYearAcres: uPriorAc || null }) : null);
        // ⚠ WHAT WE STILL NEED, stated as a request rather than as a failure.
        // Both of these are real gaps the engines already report, and both are
        // things only the grower or the FSA office can close.
        const uNeeds = [];
        // ⚠ THE PAGE MUST NOT SAY A THING CANNOT BE PRICED AND THEN PRICE IT.
        // This asked for the reported acreage and explained that "the premium and
        // the 80% vs 85% comparison cannot be priced until acres are reported" —
        // while the same sheet printed $37.96/ac and a $14.73/ac comparison, and
        // page three printed "92 ac — your 2026 acres" underneath them. Both
        // halves were true of different things: no CY2027 acreage is reported, and
        // the engine quoted on last year's instead.
        //
        // The engine already STATES which rung it used (`acreage.source` /
        // `.detail`, and PRIOR_YEAR carries "the premium is quoted on the N acres
        // last reported"). Re-deriving that claim here is what got it wrong, so the
        // ask now reads off the engine: still a real request — acres move the BU/EU
        // discount band — but no longer a denial of what the page just did.
        const uAcr = uRec && uRec.acreage;
        if (!uCurAc) uNeeds.push({
          what: "Your " + MPCI_WHEAT_YEAR + " reported wheat acreage",
          why: (uAcr && uAcr.quotable && uAcr.acres)
            ? "the guarantee and coverage above are exact without it; the premium and the " +
              "80% vs 85% comparison are quoted on the " + Math.round(uAcr.acres) +
              " acres shown on the coverage page, and are re-priced against your reported " +
              "figure once it is filed"
            : "the guarantee and coverage above are exact without it, but the premium " +
              "and the 80% vs 85% comparison cannot be priced until acres are reported" });
        // Asked for ONLY where it decides the county: in the threshold state PLC pays more
        // above the breakeven and ARC-CO below it. Where neither pays, or only one can, no
        // payment yield changes the answer, and asking for one would imply it does.
        if (arc && arc.state === "threshold") uNeeds.push({
          what: "Your FSA payment yield for wheat",
          why: "PLC pays more than ARC-CO here if it is above " + Math.round(arc.breakeven) +
               " bu — it is on your FSA farm record, and it is not your APH" });
        strategies.push(buildFarmStrategy({
          farm: farmer.name, crop: "WHEAT", cropYear: MPCI_WHEAT_YEAR, needs: uNeeds,
          // Which unit this block is for. Page one prints it only when the farm
          // has more than one block to tell apart.
          scope: { county: cs.county, state: cs.state },
          fsa: arc ? { lean: arc.label, cropYear: MPCI_WHEAT_YEAR, arcBlocksSco: ARC_BLOCKS_SCO,
                       myaIsForecast: true, myaLabelled: true, myaStatus: "forecast",
                       headline: `FSA program: ${arc.label} (at USDA's forecast price).` } : null,
          individual: uRec ? {
            coverageLevel: uRec.cl, sourceCoverageLevel: uRec.cl,
            sourceEngine: "wheat/coverageRecommend", plan: "RP", unitStructure: uRec.unit,
            // The engine states WHERE the unit structure came from; without it a
            // derived "EU" is indistinguishable from one the AIP file stated, and
            // rule 12 of checkConsistency cannot tell a priced comparison apart
            // from a priced assumption. It was computed and discarded before.
            unitSource: uRec.unitSource || null,
            cropYear: MPCI_WHEAT_YEAR, actuarialYear: MPCI_WHEAT_YEAR,
            aphCropYear: MPCI_WHEAT_YEAR, aphProvenance: uCov && uCov.aph_yield != null ? "AIP file" : null,
            // ⚠ THE OBJECT MUST STATE WHAT THE PAGE STATES. The engine computes a
            // premium on a derived unit structure so it can price a comparison at
            // all — that is legitimate, and rule 12 is not about the calculation.
            // It is about a dollar figure PRESENTED to a grower. Passing the
            // engine's internal number here made rule 12 fire on a report that
            // correctly withholds the premium everywhere on screen, stamping
            // "Draft — check before handing this to the grower" on a document with
            // nothing wrong with it. A premium is stated only where the unit is.
            premiumPerAc: (uRec.unitSource === "policy" || uRec.unitSource === "caller") && uRec.mpci
              ? uRec.mpci.netAcre : null,
            productionBasis: "aph_x_acres",
            // ⚠ THE REASON MUST NOT BE A FAILURE MESSAGE. coverageRecommend builds
            // why[0] as "Recommended X - <headline>", and where the bands could not
            // be priced that headline is "the acreage needed to price a comparison is
            // missing." That is a NEED, listed as one below; printing it as the REASON
            // told a grower we recommend 80% because we lack his acreage, which is
            // both wrong and unhelpful. Where it priced, why[0] stands unchanged.
            headline: (uRec.moves && uRec.moves.up && uRec.moves.up.netCostPerAc != null)
              ? ((uRec.why && uRec.why[0]) || null)
              : ("Recommended " + Math.round(uRec.cl * 100) + "% RP" + (uRec.sco ? " + SCO" : "") +
                 (uRec.eco ? " + ECO95" : "") + " — 80% is the benchmark every alternative has to beat, " +
                 "and nothing on this farm’s record beats it on the evidence available."),
            // A move whose cost could not be computed has no tradeoff to state.
            // It reads "$0.00/ac" otherwise — i.e. "the upgrade is free" — which is
            // the opposite of what the page beside it says.
            //
            // ⚠ AND THE MOVE HAS TO BE THE ONE WE RECOMMENDED. This read
            // `moves.up`, which is the FIXED 80->85 pair — coverageRecommend says so
            // in its own comment beside `nextLevelUp`, and warns that reading it as
            // "the next level up" holds only when the recommendation is 80%. On
            // WOODLAWN FARMS (recommended 85% from an 80% incumbent) page one
            // therefore printed "85% would cost another $14.73/ac" under **The
            // trade-off**, which is the cost of the recommendation presented as the
            // case against it. Two readings of one sentence, on the summary page.
            //
            // Recommending a CHANGE and recommending STAYING PUT have different
            // trade-offs, and both are stated as what the reader gives up:
            //   a buy-up  -> what staying at the incumbent would have saved
            //   no change -> what the next level up would cost
            mainTradeoff: (() => {
              const inc = uRec.incumbentLevel;
              const rm = uRec.recMove;
              if (rm && rm.netCostPerAc != null && uRec.recMoveDirection === "up" && inc != null) {
                return `Staying at ${Math.round(inc * 100)}% would save $${rm.netCostPerAc.toFixed(2)}/ac ` +
                  `and leave the ${Math.round(rm.from * 100)}–${Math.round(rm.to * 100)}% band on the county trigger.`;
              }
              const up = uRec.nextLevelUp;
              if (up && up.netCostPerAc != null) {
                return `${Math.round(up.to * 100)}% would cost another $${up.netCostPerAc.toFixed(2)}/ac ` +
                  `to move the ${Math.round(up.from * 100)}–${Math.round(up.to * 100)}% band to your own trigger.`;
              }
              return null;
            })(),
            // ⚠ A COMPUTATION FAILURE IS NOT A CHANGE OF MIND. Where the band
            // comparison could not be priced at all, `whyNot85` is the sentence
            // saying so — that is a NEED (above), not a reason we would reverse.
            // Both directions matter: a real reason must still reach this slot.
            changeOurMind: (uRec.moves && uRec.moves.up && uRec.moves.up.netCostPerAc != null)
              ? (uRec.whyNot85 || null) : null,
          } : null,
          area: uRec ? {
            elected: !!(uRec.sco || uRec.eco), scoElected: !!uRec.sco, ecoElected: !!uRec.eco,
            scoTopPct: WHEAT_SCO_TOP, ecoTopPct: 95, baseCoverageLevel: uRec.cl,
            cropYear: MPCI_WHEAT_YEAR, offerExists: !!(uRec.scoOffered || uRec.ecoOffered),
            premiumPerAc: (() => {
              // An endorsement not elected contributes a real 0; one elected but
              // not priced makes the whole figure UNKNOWN, never cheaper.
              const s = uRec.sco ? uRec.sco.netAcre : 0;
              const e = uRec.eco ? uRec.eco.netAcre : 0;
              return (s == null || e == null) ? null : s + e;
            })(),
            fitState: uRec.fitState, fitLabel: uRec.fitLabel,
          } : null,
          futures: mkt && mkt.contract ? {
            cropYear: 2027, contractLabel: mkt.contract.label || mkt.contract.sym || null,
            quantitiesShown: false, productionBasis: null,
            targets: (((mkt.targets && mkt.targets.rungs) || [])).slice(0, 3).map(t => ({ price: t.lvl != null ? t.lvl : t.price })),
          } : null,
        }));
      }
      // ── THE STANDARD REPORT (V2) ─────────────────────────────────────────────
      // Kevin, 2026-09-16: a report an agent can hand to a farmer and explain in two or
      // three minutes — recommendation, cost, change, reason, evidence, missing
      // information. Built from the quote page's own facts; the pages above become the
      // optional Detailed Coverage Analysis appendix.
      const unitFacts = Object.values(stratFacts);
      // The county trigger record at the RECOMMENDED level and endorsements, through the
      // same wheatTriggerStats the detailed trigger page uses.
      const trigStates = [...new Set(unitFacts.filter(u => u.proposed && (u.proposed.sco || u.proposed.eco)).map(u => u.state).filter(Boolean))];
      const trigRows = trigStates.length ? await sbFetch("sco_eco_county", {
        filters: ["crop=eq.WHEAT", `state=in.(${trigStates.join(",")})`],
        select: "crop_year,state,county,revenue_ratio,projected_price",
      }).catch(() => []) : [];
      for (const u of unitFacts) {
        if (!u.proposed || !(u.proposed.sco || u.proposed.eco)) continue;
        const ts = wheatTriggerStats({ aph: u.aph, baseCl: u.proposed.level,
          rows: (trigRows || []).filter(x => (x.state || "").toUpperCase() === u.state && (x.county || "").toUpperCase() === u.county) },
          { sco: u.proposed.sco, eco: u.proposed.eco });
        if (!ts || ts.A == null) continue;
        u.trigger = { n: ts.N, scoYears: ts.scoN, ecoYears: ts.ecoN, avgAll: ts.avgAll, showSco: ts.showSco, showEco: ts.showEco,
          firstYear: ts.years[0].yr, lastYear: ts.years[ts.N - 1].yr,
          worst: ts.worst ? { year: ts.worst.yr, amount: ts.indem(ts.worst) } : null,
          years: ts.years.map(y => ({ year: y.yr, ratio: y.ratio, paid: ts.indem(y) })) };
      }
      const pricedUnits = unitFacts.filter(u => u.price && u.price.value > 0);
      // ── THE PRICE'S STATUS IS THE PRICE LADDER'S, NOT A SECOND DEFINITION ──────
      // rmaPriceDisplay(resolveRmaPrice(...)) is the one rule for what a reader is told about
      // a resolved RMA price (#192: the PM-26-056 approval citation, ADM graduation, disputes).
      // It describes the RESOLVED price, so it is printed only where that is the same published
      // cent this report prices on. A quote on any other figure — a price typed on the quoter —
      // prints its value with no status, rather than a status that belongs to another number.
      const ladderPrice = resolveRmaPrice("WHEAT", MPCI_WHEAT_YEAR, "projected");
      const reportPriceDisplay = (pricedUnits.length && ladderPrice && ladderPrice.price != null
          && rmaSameCent(ladderPrice.price, pricedUnits[0].price.value))
        ? rmaPriceDisplay(ladderPrice, "projected") : null;
      const pxVals = [...new Set(pricedUnits.map(u => (+u.price.value).toFixed(2)))];
      const reportPrice = pricedUnits.length === 0 ? null
        : { value: pxVals.length === 1 ? +pricedUnits[0].price.value : null, display: reportPriceDisplay };
      // countyBasis "insurance": these are the counties the wheat is INSURED in; Silas holds no FSA
      // base-acre record, so the report labels its FSA section an insurance-county scenario.
      const fsaIn = arcRowsOut.length ? { rows: arcRowsOut, paymentYield: null, programYear: arcProgramYear, basis: arcBasisOut, countyBasis: "insurance" }
        : (arcPendingMsg ? { rows: [], pending: arcPendingMsg, programYear: arcProgramYear } : null);
      // A reach figure is a share of past days on which the NEARBY series rose this far within the
      // fill horizon; the report says so, with the settle date the board figure is from.
      const marketIn = (mkt && mkt.targets && mkt.tc) ? { contractLabel: mkt.contract.sym + " (" + _rptMON[mkt.contract.mo] + " '" + String(mkt.contract.yr).slice(2) + ")",
          current: mkt.tc.last, asOf: mkt.asOf || null, reachTradingDays: mkt.fillHorizon || null, lookbackYears: 5 /* grainFillCurve reads the last 1,260 bars */,
          // The board's OWN remaining life, beside the bucket the fill rate was measured over.
          // grainFillCurve answers with the nearest of six fixed horizons, so the two differ by
          // up to a month and the sheet may not present one as the other.
          boardTradingDays: (mkt.horizon && mkt.horizon.tradingDays != null) ? mkt.horizon.tradingDays : null,
          projectedPriceContract: WHEAT_2027.contract,
          levels: (mkt.targets.rungs || []).map(t => ({ price: t.lvl, reach: t.fill })) } : null;
      const vm = synthesizeStrategyReport({
        farm: farmer.name, crop: "WHEAT", cropYear: MPCI_WHEAT_YEAR, units: unitFacts,
        fsa: fsaIn,
        market: marketIn,
        price: reportPrice,
        // A strategy the consistency engine marks INCOMPLETE prints as a DRAFT, with the
        // reason — never quietly as a finished report.
        findings: strategies.filter(st => st.state === STRATEGY_STATE.INCOMPLETE).flatMap(st => st.blocking),
      });
      // Read by tools/fsr-capture.js for acceptance evidence — this agent's own view of
      // this farm, kept in the page and sent nowhere.
      window.__fsrLast = { vm, units: unitFacts, inputs: { fsa: fsaIn, market: marketIn, price: reportPrice },
        // Why the election sheet is missing, when it is — so a capture (tools/fsr-capture.js) can
        // SEE the reason instead of an absent page that looks like a feature nobody built.
        arcSheet: { rendered: !!arcPage, note: arcSheetNote } };
      if (arcSheetNote) console.warn("Full Strategy Report: no ARC/PLC election sheet — " + arcSheetNote);
      // ⚠ THE ELECTION SHEET IS NOT PART OF THE OPTIONAL APPENDIX (Kevin, 2026-09-19: "use that
      // exact sheet in the full strategy report"). `printSec.appendix` is OFF by default —
      // WHEAT_PRINT_SECTIONS carries no `appendix` key at all — so anything behind it prints for
      // nobody until an agent ticks a box, which is how the sheet was absent from every report.
      // ⚠ THE QUOTE IS NOT OPTIONAL EITHER (Kevin, 2026-09-21). The report's order is the one he
      // asked for: the synthesis pages, then the QUOTE exactly as Print Quote shows it, then the
      // ARC/PLC election sheet, then the cash wheat targets. Only the technical notes and the
      // market-context page remain behind "Detailed appendix", which is off by default.
      // ⚠ THE REPORT REFUSES TO PRINT RATHER THAN PRINT "PREMIUM NOT QUOTED".
      //
      // Kevin, 2026-09-22: *"Don't generate a finished customer strategy report
      // containing 'Premium not quoted'. If required information such as unit
      // structure is missing, stop report generation and tell the agent exactly
      // what input must be supplied."*
      //
      // This REPLACES the honest-withholding behaviour on the CUSTOMER document
      // only — the on-screen card and the quote still withhold and say why, which
      // is right there because the agent is the reader and can act on it. On a
      // sheet a grower keeps, a column of dashes is not a caveat, it is an
      // unfinished document. An agent may STATE a unit structure the AIP has not
      // keyed, so this is always clearable.
      const missingUnit = (unitFacts || []).filter(u => !(u.unit && u.unit.value));
      if (missingUnit.length) {
        // ⚠ A REFUSAL IS ONLY AS GOOD AS THE INSTRUCTION IN IT. Kevin hit this on a
        // phone: the block was correct and the remedy read "Unit structure →
        // Enterprise / Basic", while the control on screen is labelled **Structure**
        // and sits on that county's OWN card, below the button he had just pressed.
        // A name that does not appear anywhere on the page sends someone hunting.
        //
        // Named in Title Case as the cards print them, so "Ontario" on screen is
        // "Ontario" here rather than the mirror's ONTARIO.
        const nice = (c) => String(c || "").replace(/\b([A-Z])([A-Z']*)\b/g,
          (_, a, b) => a + b.toLowerCase());
        const names = missingUnit.map(u => nice(u.county) + (u.state ? ", " + u.state : "")).join(" · ");
        const one = missingUnit.length === 1;
        // ⚠ THE CARD ALREADY SAYS "EU ASSUMED" AND THIS SAID "NOT ON FILE", AND BOTH WERE TRUE
        // OF DIFFERENT THINGS (Kevin, 2026-09-23: "FSR still not correct, it's saying we don't
        // have unit structure when we do"). The agent's card quotes on an ASSUMED structure and
        // badges it; the customer report refuses, which is Kevin's own 2026-09-22 rule that a
        // finished report must not print an empty premium column. Two standards, deliberately —
        // but the remedy sent him hunting for a dropdown on another card, and read as the report
        // denying something the screen above it had just asserted.
        //
        // So the refusal stands and the REMEDY becomes one click: Silas already has a structure
        // it is quoting at (`proposed.unit`), and an agent confirming it is the agent STATING it,
        // which is exactly what the rule asks for. Nothing is inferred — the agent affirms, and
        // `unitEntry` records it as an agent statement, not as policy data.
        const UN = { EU: "Enterprise", BU: "Basic", OU: "Optional" };
        const offerable = missingUnit.filter(u => u.policyId && u.proposed && UN[u.proposed.unit]);
        if (offerable.length === missingUnit.length) {
          const list = offerable.map(u => "  · " + nice(u.county) + " → " + UN[u.proposed.unit]).join("\n");
          if (window.confirm("This report needs a unit structure for: " + names
            + "\n\nWithout it the premium cannot be quoted, and a customer report must not go out "
            + "with an empty premium column.\n\nSilas is quoting " + (one ? "it" : "them") + " at:\n" + list
            + "\n\nSet " + (one ? "that structure" : "those structures") + " and build the report?"
            + "\n\nThis records it as YOUR statement of the structure, not as something the AIP sent. "
            + "You can change it on the county's card at any time.")) {
            const next = { ...unitEntry };
            for (const u of offerable) next[u.policyId + "|" + u.key] = u.proposed.unit;
            setUnitEntry(next);
            // ⚠ The pages above were already built from the OLD entries, so this cannot simply
            // continue — it would print the quote it just refused. The state is set; one more
            // press rebuilds everything from it. Rebuilding in place would mean re-running the
            // whole quote pipeline mid-function, which is a far larger change to make on a
            // document that is otherwise working.
            alert("Structure set for " + names + ".\n\nPress Full Strategy Report again to build it.");
          }
          return;
        }
        alert("This report is not finished yet.\n\n"
          + "No unit structure is on file for: " + names
          + "\n\nWithout it the premium cannot be quoted, and a customer report must not go out with an empty premium column."
          + "\n\nScroll down to " + (one ? nice(missingUnit[0].county) + "'s card" : "each of those county cards")
          + " on this page and set “Structure” to Enterprise, Basic or Optional — "
          + "it sits beside the coverage level. Then press Full Strategy Report again.");
        return;
      }
      // The per-county quote pages, the trigger record, the market page and the
      // technical notes are AGENT detail. They repeated page one five times per
      // county; behind the appendix they stay available and print for nobody by
      // default. See the page plan in strategyReportHtml.
      // ⚠ `mktPage` IS PAGE FIVE NOW AND MUST NOT ALSO SIT IN THE APPENDIX — an agent
      // who ticks "Detailed appendix" would otherwise get the futures levels twice.
      // The CASH targets move here instead: still built, still available, and no
      // longer in front of a grower on a page Kevin asked to be futures-only.
      const appendix = printSec.appendix ? (quotePages + trigPage + cashTargets + technicalNotesHtml(unitFacts, logo)) : "";
      // ⚠ THE CASH TARGETS PAGE LEAVES THE CUSTOMER REPORT (Kevin, 2026-09-23):
      // "just futures level targets based on a fundamental" — not cash, not a
      // delivery point. A cash ladder converts at a basis a NAMED elevator posted,
      // so it is a delivery-point quote by construction and cannot be made to
      // comply by rewording it. It still builds; it prints from Cash Targets, which
      // is where an agent asks that question. `mktPage` is page five instead — the
      // same board's own technical levels with how often this market has reached
      // that far, which is what he asked for and which already carries the "not a
      // recommendation to sell" voice from the 2026-09-01 rewrite.
      const all = strategyReportHtml(vm, { logoSrc: logo, dateStr: new Date().toLocaleDateString(), units: unitFacts,
        bands2027: scoEcoBands(MPCI_WHEAT_YEAR), quoteHtml: "", electionSheetHtml: arcPage,
        comparePagesHtml: cmpTrigPages, futuresLevelsHtml: mktPage,
        appendixHtml: appendix }) || (arcPage + mktPage);
      if (!all) { alert("Nothing to print for this farm."); return; }
      openWheatPrintDoc(`${farmer.name} — ${MPCI_WHEAT_YEAR} Wheat Coverage Strategy`, all);
    } catch (e) { alert("Report failed: " + (e && e.message || e)); }
    finally { setReportBusy(false); }
  };

  // Batch print — fetches each selected farm's data and stacks all their county pages into one document.
  const printWheatBatch = async (selFarms) => {
    setBatchBusy(true);
    try {
      const logo = await loadLogoDataUri() || `${window.location.origin}/Clover_Ag_Logo1_transparent.png`;
      const price = +px.price, vol = +px.vol;
      let all = "";
      for (const f of selFarms) {
        const pids = (f.allPolicyIds || f._allPolicyIds || []); if (!pids.length) continue;
        const flt = pids.join(",");
        try {
          // ⚠ THE SAME COLUMNS THE SINGLE-FARM LOAD READS, and this farm's APH DATABASES. The
          // batch select had drifted short of it — no `practice` on production_history, so an
          // organic unit's yield series came back empty here and full on the card — and it
          // fetched no databases at all, so every batch page priced the CY2027 summary row.
          const [c, p, pol, u] = await Promise.all([
            sbFetch("crop_coverage", { filters:[`policy_id=in.(${flt})`], select:"policy_id,crop,county,type,practice,practice_code,aph_yield,rate_yield,adjusted_yield,yield_options,insurance_options,coverage_level,plan,price_election,price_election_pct,acres,liability,net_premium,total_premium,subsidy,admin_fee_waiver_code,unit_structure,mpci_plan_code,iif_key" }),
            sbFetch("production_history", { filters:[`policy_id=in.(${flt})`], select:"crop,county,type,practice,crop_year,acres,yield_per_acre,record_type,excluded_from_avg" }),
            sbFetch("policies", { filters:[`id=in.(${flt})`], select:"id,state,policy_number,crop_year" }),
            sbFetch("crop_coverage_units", { filters:[`policy_id=in.(${flt})`], select:"policy_id,county,crop,plan,practice_code,unit_number,sub_unit,acres,aph_yield,rate_yield,adjusted_yield,yield_options,is_prevent_plant" }).catch(() => []),
          ]);
          const pStates = Object.fromEntries((pol||[]).map(x => [String(x.id), (x.state||"").toUpperCase()]));
          const pNumbers = Object.fromEntries((pol||[]).map(x => [String(x.id), x.policy_number || ""]));
          // policy_id -> crop_year, so the batch collapses county x practice across crop years
          // exactly like the single-farm print and the screen do.
          const pYearsB = Object.fromEntries((pol||[]).map(x => [String(x.id), x.crop_year]));
          // No longer skipped — the engine is validated book-wide. A farm with a big trend
          // adjustment (effective index above the top ADM level) still prints; its premium is
          // within ~1% (over/safe) and its coverage is exact.
          const batchLevels = {};
          // No `agent`: the on-screen APH edit and database ticks belong to the loaded farm, and
          // a batch walks other farms. Their databases still weigh exactly as on the card.
          const batchAph = { units: u || [], agent: null };
          all += wheatQuotePages(f.name, c || [], p || [], pStates, pNumbers, price, vol, logo, null, pYearsB, undefined, batchLevels, undefined, undefined, { aph: batchAph });
          // Each farm's packet carries its own trigger record, same as the single print.
          // It is a second sheet per farm — deliberate: a renewal packet without the
          // evidence behind SCO/ECO is the gap this page was added to close.
          all += await wheatTriggerPage(f.name, c || [], p || [], pStates, pYearsB, btBands, logo, false, batchLevels, batchAph);
        } catch { /* skip a farm that fails to load */ }
      }
      if (!all) { alert("Nothing to print (selected farms have no quotable wheat units)."); return; }
      openWheatPrintDoc("Batch — 2027 Wheat Quotes", all);
    } finally { setBatchBusy(false); }
  };

  // value ladder: producer $/ac vs government subsidy $/ac for the full MPCI+SCO+ECO stack at each level
  // recCl / recUnit MUST come from the same recommendWheat() call that produced the
  // recommendation box above this ladder. They used to be hardcoded here (85% / acres>=20),
  // so the ladder happily flagged "85% — recommended" directly under a box recommending 80%.
  // If no recommendation is passed, mark nothing rather than inventing one.
  function recLadder(state, county, aph, acres, price, vol, recCl, recUnit, opts = {}) {
    const gridBfr = !!opts.bfr;
    const prac = opts.practice || "003";
    const A = +aph, AC = +acres || 1; county = (county || "").toUpperCase();
    if (!wheatCountyInSet(state, county, prac) || !A || !(+price > 0)) return null;
    const unit = recUnit || (AC >= 20 ? "EU" : "BU");
    const tdG = { padding:"4px 9px", fontSize:12, borderTop:"1px solid #eef3ee", textAlign:"right" };
    const thG = { padding:"5px 9px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".03em", color:"#677167", fontWeight:700, textAlign:"right" };
    return (
      <div style={{ overflowX:"auto", marginTop:8 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:440 }}>
          <thead><tr><th style={{ ...thG, textAlign:"left" }}>MPCI level (+{areaCoverageLabel("SCO", opts.scoPct)}+{areaCoverageLabel("ECO", opts.ecoPct)})</th><th style={thG}>You pay $/ac</th><th style={thG}>Subsidy $/ac</th><th style={thG}>Coverage $/ac</th></tr></thead>
          <tbody>
            {LEVELS.map(cl => {
              const m = quoteWheatRP2027({ state, county, aph:A, cl, acres:AC, unit, price:+price, vol:+vol, plan:"RP", bfr:gridBfr, practice:prac });
              const s = quoteAreaRP2027({ state, county, endorsement:"SCO", baseCl:cl, aph:A, acres:AC, price:+price, vol:+vol, bfr:gridBfr, practice:prac, coveragePct: areaCoverageFractionOfPct(opts.scoPct) });
              const e = quoteAreaRP2027({ state, county, endorsement:"ECO", baseCl:cl, aph:A, acres:AC, price:+price, vol:+vol, bfr:gridBfr, practice:prac, coveragePct: areaCoverageFractionOfPct(opts.ecoPct) });
              if (!m) return null;
              const net = m.netAcre + (s?s.netAcre:0) + (e?e.netAcre:0);
              const gross = (m.grossTotal + (s?s.grossTotal:0) + (e?e.grossTotal:0)) / AC;
              const cov = m.liabPerAc;   // farm-level guarantee $/ac (SCO/ECO area on top)
              const rec = recCl != null && Math.abs(cl - recCl) < 1e-9;
              return (
                <tr key={cl} style={{ background: rec ? "#eef6ee" : undefined }}>
                  <td style={{ ...tdG, textAlign:"left", fontWeight: rec ? 800 : 600, color: rec ? "#1f5c2e" : "#1a2e1a" }}>{Math.round(cl*100)}% {unit==="EU"?"Ent":"Basic"}{rec ? "  — recommended" : ""}</td>
                  <td style={{ ...tdG, fontWeight: rec ? 800 : 500 }}>{$ac(net)}</td>
                  <td style={{ ...tdG, color:"#1f7a3d" }}>{$ac(gross - net)}</td>
                  <td style={{ ...tdG, color:"#5c6f5c" }}>{$ac(cov)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ fontSize:10, color:"#677167", marginTop:5 }}>Full stack: MPCI RP {AC>=20?"Enterprise":"Basic"} + SCO (→{Math.round(scoEcoBands(MPCI_WHEAT_YEAR).scoTop*100)}%) + ECO ({Math.round(scoEcoBands(MPCI_WHEAT_YEAR).ecoBottom*100)}→{Math.round(scoEcoBands(MPCI_WHEAT_YEAR).ecoTop*100)}%). "Subsidy" = the government-paid share, $/ac. "Coverage" = your farm-level guarantee; SCO/ECO add county-level protection on top.</div>
      </div>
    );
  }
  const CTY_OPTS = (WHEAT_COUNTIES_BY_STATE[qi.state] || []).slice().sort();
  // `prac` shows the price that ACTUALLY drives the quote in view. Certified organic runs on
  // RMA's own election ($11.16 vs $5.76 on 2026 wheat), so leaving the bar showing the
  // conventional number beside an organic quote would read as a bug in the engine.
  const priceBarOf = (prac = "003", st = null) => {
    const q = pxFor(prac, st);
    return (
    <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap", fontSize:12, color:"#5c6f5c" }}>
      <span>Projected price <input type="number" step="0.01" value={px.price} onChange={e => setPx(p => ({ ...p, price:e.target.value, status:"manual" }))} style={{ width:66, marginLeft:6, fontSize:12.5, padding:"3px 6px", borderRadius:6, border:"1px solid #d5e2d5", textAlign:"right" }} />/bu</span>
      <span>Volatility <input type="number" step="0.01" value={px.vol} onChange={e => setPx(p => ({ ...p, vol:e.target.value, volStatus:"entered" }))} style={{ width:56, marginLeft:6, fontSize:12.5, padding:"3px 6px", borderRadius:6, border:"1px solid #d5e2d5", textAlign:"right" }} /></span>
      {/* The organic election is a SET figure. When RMA has announced it the feed supplies it
          and the box is read-only; when it hasn't, there is nothing to show but somewhere to
          put the announced number once Kevin has it. Never a derived value. */}
      {isCertifiedOrganic(prac) && (
        q.status === "rma" ? (
          <span style={{ fontSize:11.5, fontWeight:700, color:"#7a5a10", background:"#fbf6ea", border:"1px solid #e3d3ab", borderRadius:6, padding:"3px 9px" }}
                title={q.source === "bulletin" ? `RMA's announced certified-organic price election — RMA ${q.bulletin}` : "RMA's announced certified-organic price election, from the ADM feed"}>
            🌱 Organic election ${q.price.toFixed(2)}/bu
            <span style={{ fontWeight:600, color:"#8a7a4a", marginLeft:6 }}>RMA announced</span>
          </span>
        ) : (
          <span style={{ fontSize:11.5, color:"#7a5a10", background:"#fbf6ea", border:"1px solid #e3d3ab", borderRadius:6, padding:"3px 9px" }} title={q.note}>
            🌱 Organic election
            <input type="number" step="0.01" placeholder="not announced" value={pxOrg}
                   onChange={e => setPxOrg(e.target.value)}
                   style={{ width:92, margin:"0 5px", fontSize:12.5, padding:"3px 6px", borderRadius:6,
                            border:"1px solid " + (+pxOrg > 0 ? "#c9b27a" : "#e0b9a8"), textAlign:"right",
                            background:"#fff" }} />
            <span style={{ fontWeight:700, color: +pxOrg > 0 ? "#7a5a10" : "#a5461b" }}>
              {+pxOrg > 0 ? "entered by hand" : `RMA hasn't announced ${MPCI_WHEAT_YEAR} — no organic quote until it does`}
            </span>
          </span>
        )
      )}
      {/* ⚠ RMA PUBLISHES THE PRICE AND THE VOLATILITY SEPARATELY, AND THIS BANNER
          USED TO CLAIM BOTH. The query that adopts an official row filters only on
          `projected_price=not.is.null`, so it fires the moment RMA posts a price —
          and CY2027 `rma_prices` today holds 10,119 rows, EVERY ONE of them with a
          NULL volatility. (CY2026: 9,255 of 22,485.) So "price + volatility as
          published — no longer an estimate" was days from being false: the price
          would be RMA’s and the volatility would still be our 0.27 estimate, with
          the sheet printing it and the screen saying it was official.

          The adoption itself is already right — `vol: conv.vol != null ? conv.vol :
          p.vol` keeps the estimate rather than nulling the premium. What was wrong is
          only what we SAY about it, and that is the half a grower reads. Per input. */}
      {pxRma ? (
        volOfficial ? (
          <span style={{ fontSize:11, fontWeight:800, color:"#1f5c2e", background:"#eaf3ea", border:"1px solid #b7d3b7", borderRadius:6, padding:"3px 9px" }}>
            ✓ RMA OFFICIAL — price and volatility as published{pxRma.released_date ? ` (${pxRma.released_date})` : ""}. No longer an estimate.
          </span>
        ) : (
          <span style={{ fontSize:11, fontWeight:800, color:"#8a6d1f", background:"#fbf7ee", border:"1px solid #e3d3ab", borderRadius:6, padding:"3px 9px" }}>
            ✓ RMA price official{pxRma.released_date ? ` (${pxRma.released_date})` : ""} — <b>volatility not published yet</b>, so every premium below still uses the {(+px.vol).toFixed(2)} estimate.
          </span>
        )
      ) : (
        <span style={{ fontSize:11, color:"#677167" }}>{pxAuto ? (pxAuto.status === "tracking" ? `tracking Sep-27 CBOT avg · ${pxAuto.days}d` : `Sep-27 CBOT latest ${pxAuto.asOf}`) : `estimate — Sep-27 CBOT SRW, discovery Aug 15–Sep 14 2026`}</span>
      )}
      {/* Spot-checking against the AIP is the whole point of these boxes being editable. Before
          RMA publishes, ours and the AIP's are BOTH guesses and they differ (we track the live
          Sep-27 board; theirs lags). Compare without matching them and the engine looks broken
          when it isn't. Once RMA publishes, there is nothing left to match — both sides are on
          the official number and a mismatch is a real bug. */}
      {!pxRma && (
        <span style={{ fontSize:10.5, color:"#8a6d1f", background:"#fbf7ee", border:"1px dashed #e3d3ab", borderRadius:6, padding:"3px 8px" }}>
          ⓘ Spot-checking vs the AIP? <b>Type their price &amp; vol in here first.</b> Neither is RMA-set until ~Sep 15 — ours tracks the live board, theirs lags. The engine matches to the cent once the inputs match.
        </span>
      )}
    </div>
    );
  };
  // Official per INPUT. RMA posts the price and the volatility on their own
  // schedules and the adoption query only requires a price, so "official" is not
  // one state — see the banner below.
  const volOfficial = px.volStatus === "rma";
  const priceBar = priceBarOf("003");

  // ── THE BLANK QUOTE'S ONE CONFIGURATION ─────────────────────────────────────
  // Built once per render and handed to every surface that prices it: the builder
  // panel, the level grid under it, and the printed sheet. See
  // src/wheat/blankQuotePrint.js for why there is one call and not three.
  //
  // ⚠ THE ACREAGE IS RESOLVED, NEVER `|| 1`. The builder used to read `+qi.acres || 1`,
  // so clearing the box priced every premium on ONE acre — the top of the BU/EU
  // discount band, up to ~14% high — with nothing on screen to say so. A typed figure is
  // PLANNED; nothing typed is the disclosed 100-acre assumption Kevin set for printing a
  // quote quickly (2026-09-02), and every surface names which one it is.
  const blankPx = pxFor(qi.practice, qi.state);
  const blankBasis = resolveAcreageBasis({ plannedAcres: qi.acres, assumedAcres: DEFAULT_QUOTE_ACRES });
  const blankCfg = {
    state: qi.state, county: qi.county, practice: qi.practice, aph: qi.aph, cl: qi.cl,
    unit: qi.unit, plan: qi.plan, sco: !!qi.sco, eco: !!qi.eco, scoPct: qi.scoPct, ecoPct: qi.ecoPct,
    bfr: !!qi.bfr, acres: blankBasis.acres, price: blankPx.price, vol: blankPx.vol,
  };
  const blankQuote = blankQuoteCoverage(blankCfg, WHEAT_BLANK_ENG);
  const printBlankWheatQuote = async () => {
    const logo = await loadLogoDataUri() || `${window.location.origin}/Clover_Ag_Logo1_transparent.png`;
    const built = aphb.on && aphCalc && aphCalc.approved != null;
    const aphSource = built ? "built"
      : (aphRefs.tYield != null && String(qi.aph) === String(aphRefs.tYield) ? "tyield" : "entered");
    const res = blankQuotePrintHtml({
      cfg: blankCfg, levels: LEVELS, acreage: blankBasis, cropYear: MPCI_WHEAT_YEAR,
      bands: scoEcoBands(MPCI_WHEAT_YEAR), logoSrc: logo, prospect: qi.prospect,
      countyLabel: capCty(qi.county), stateLabel: FIPS_STATE[qi.state] || WHEAT_STATES[qi.state] || "",
      practiceLabel: WHEAT_PRACTICE_NAMES[qi.practice] || qi.practice,
      priceStatus: blankPx.status, volStatus: blankPx.volStatus,
      aph: {
        source: aphSource, tYield: aphRefs.tYield,
        label: built ? aphProvenance({ edited: true }).label : aphSource === "tyield" ? "County T-yield" : "APH (entered)",
        build: built ? {
          rows: aphb.rows.filter(r => +r.year && +r.y > 0).map(r => ({ year: +r.year, y: +r.y })),
          options: [aphb.ta && "TA", aphb.ya && "YA", aphb.yc && "YC", aphb.ye && "YE"].filter(Boolean),
          approved: aphCalc.approved,
          detail: [
            `${aphCalc.nActual} yr${aphCalc.nActual === 1 ? "" : "s"} actual`,
            aphCalc.nExcluded ? `${aphCalc.nExcluded} excluded` : null,
            aphCalc.nFloored ? `${aphCalc.nFloored} floored (YA)` : null,
            aphCalc.taBu ? `+${aphCalc.taBu} TA` : null,
            aphCalc.ycFloored ? "cupped (YC)" : null,
          ].filter(Boolean).join(" · "),
        } : null,
      },
      options: { bfr: !!qi.bfr, mc: !!qi.mc && qi.unit === "EU", ql: !!qi.ql },
      dateLabel: new Date().toLocaleDateString(),
    }, WHEAT_BLANK_ENG);
    if (!res.ok) { alert("Can't print this quote: " + res.reason); return; }
    const who = String(qi.prospect || "").trim();
    openWheatPrintDoc(`${who ? who + " — " : ""}${capCty(qi.county)} ${MPCI_WHEAT_YEAR} Wheat Quote`, res.html);
  };

  return (
    <div>
      {/* crop selector */}
      <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontSize:11, color:"#5c6f5c", fontWeight:700, marginRight:2, textTransform:"uppercase", letterSpacing:".05em" }}>Crop</span>
        {CA_ORDER.map(k => (
          <button key={k} onClick={() => setCrop(k)} style={{ fontSize:12.5, fontWeight:700, padding:"6px 13px", borderRadius:7, cursor:"pointer", border:"1px solid " + (crop === k ? "#2d6a2d" : "#d5e2d5"), background:crop === k ? "#eaf3ea" : "#fff", color:crop === k ? "#1f7a3d" : "#5c6f5c" }}>{CA_CROPS[k].label}</button>
        ))}
        {/* PRF sits with the crops because that is what it is — a product an
            agent picks when deciding what to quote, not a separate tool. It
            hands off to the PRF Advisor rather than rendering inline: PRF is
            map-first and quoted per grid, so it does not share this panel's
            farm-and-APH shape. */}
        <button onClick={onPrf}
          style={{ fontSize:12.5, fontWeight:700, padding:"6px 13px", borderRadius:7, cursor:"pointer",
                   border:"1px solid #d5e2d5", background:"#fff", color:"#5c6f5c" }}>
          Pasture / Rangeland / Forage →
        </button>
        <span style={{ width:1, height:20, background:"#e0e8e0", margin:"0 6px" }} />
        {[["farm","From a policy"],["blank","Blank quote"]].map(([m, lbl]) => (
          <button key={m} onClick={() => setMode(m)} style={{ fontSize:12.5, fontWeight:700, padding:"6px 13px", borderRadius:7, cursor:"pointer", border:"1px solid " + (mode === m ? "#2d6a2d" : "#d5e2d5"), background:mode === m ? "#eaf3ea" : "#fff", color:mode === m ? "#1f7a3d" : "#5c6f5c" }}>{lbl}</button>
        ))}
      </div>

      {/* ── BLANK QUOTE: generic, not tied to a farmer ── */}
      {mode === "blank" && crop === "WHEAT" && (
        <div style={sect}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:10 }}>
            <h3 style={{ margin:0, fontSize:15, color:"#1a2e1a" }}>Blank wheat quote <span style={{ fontSize:11, fontWeight:600, color:"#1f7a3d" }}>AIP-validated · $/acre</span></h3>
            {/* The printed sheet prices through the SAME blankQuoteCoverage call as the
                panel below, so what prints is what is on screen. Disabled, with the reason,
                whenever the quote cannot be priced — never a sheet of dashes. */}
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              <input value={qi.prospect || ""} onChange={e => setQi(s => ({ ...s, prospect:e.target.value }))}
                placeholder="Prepared for (optional)" autoComplete="off" name="blank-quote-prospect"
                style={{ fontSize:12.5, padding:"6px 9px", borderRadius:7, border:"1px solid #d5e2d5", width:190 }} />
              <button onClick={printBlankWheatQuote} disabled={!blankQuote.ok}
                title={blankQuote.ok ? "Print this quote, or save it as a PDF" : blankQuote.reason}
                style={{ fontSize:12, fontWeight:700, padding:"6px 14px", borderRadius:7, border:"1px solid " + (blankQuote.ok ? "#2d6a2d" : "#b7c7b7"),
                         background: blankQuote.ok ? "#2d6a2d" : "#b7c7b7", color:"#fff", cursor: blankQuote.ok ? "pointer" : "default" }}>🖨 Print quote</button>
            </div>
          </div>
          <div style={{ display:"flex", gap:14, flexWrap:"wrap", alignItems:"flex-end", marginBottom:12 }}>
            <label style={{ fontSize:11, color:"#5c6f5c", fontWeight:700 }}>State<br/>
              <select value={qi.state} onChange={e => { const ns = e.target.value; const first = (WHEAT_COUNTIES_BY_STATE[ns] || []).slice().sort()[0] || ""; setQi(s => ({ ...s, state:ns, county:first, practice: wheatPractices2027(ns, first).includes(s.practice) ? s.practice : "003" })); }} style={{ marginTop:3, fontSize:13, padding:"6px 8px", borderRadius:7, border:"1px solid #d5e2d5", minWidth:120 }}>
                {Object.keys(WHEAT_STATES).map(fp => <option key={fp} value={fp}>{WHEAT_STATES[fp]}</option>)}
              </select>
            </label>
            <label style={{ fontSize:11, color:"#5c6f5c", fontWeight:700 }}>County<br/>
              {/* Not every county offers every practice — fall back rather than quote an
                  organic election against a conventional rate table. */}
              <select value={qi.county} onChange={e => setQi(s => ({ ...s, county:e.target.value, practice: wheatPractices2027(s.state, e.target.value).includes(s.practice) ? s.practice : "003" }))} style={{ marginTop:3, fontSize:13, padding:"6px 8px", borderRadius:7, border:"1px solid #d5e2d5", minWidth:150 }}>
                {CTY_OPTS.map(c => <option key={c} value={c}>{capCty(c)}</option>)}
              </select>
            </label>
            {/* Practice drives a DIFFERENT rate table, a different T-yield and a different
                price election — not a modifier on the conventional quote. Only the practices
                RMA actually offers in the selected county are listed. */}
            <label style={{ fontSize:11, color:"#5c6f5c", fontWeight:700 }}>Practice<br/>
              <select value={qi.practice} onChange={e => setQi(s => ({ ...s, practice:e.target.value }))}
                style={{ marginTop:3, fontSize:13, padding:"6px 8px", borderRadius:7, minWidth:150,
                         border:"1px solid " + (qi.practice === "003" ? "#d5e2d5" : "#c9b27a"),
                         background: qi.practice === "003" ? "#fff" : "#fdfaf1" }}>
                {(wheatPractices2027(qi.state, qi.county).length ? wheatPractices2027(qi.state, qi.county) : ["003"])
                  .map(pc => <option key={pc} value={pc}>{WHEAT_PRACTICE_NAMES[pc] || pc}</option>)}
              </select>
            </label>
            <label style={{ fontSize:11, color:"#5c6f5c", fontWeight:700 }}>APH (bu){aphb.on && <span style={{ color:"#1f7a3d", fontWeight:600 }}> · built</span>}<br/>
              <input type="number" value={qi.aph} readOnly={aphb.on} onChange={e => setQi(s => ({ ...s, aph:e.target.value, aphTouched:true }))} style={{ marginTop:3, width:80, fontSize:13, padding:"6px 8px", borderRadius:7, border:"1px solid "+(aphb.on?"#b7d3b7":"#d5e2d5"), background:aphb.on?"#f0f6f0":"#fff" }} />
              {/* WHOSE NUMBER IS IN THE BOX. RMA's T-yield is published per county AND practice
                  (Genesee 77, Orleans 74, Livingston 76 -- it moves), so a card that silently
                  carried one county's figure into another would be quoting a guarantee nobody
                  is entitled to. It renders on the plain quoter, not behind the APH builder. */}
              {!aphb.on && aphRefs.tYield != null && (
                <div style={{ fontSize:10, fontWeight:600, color:"#6b7f6b", marginTop:3, lineHeight:1.35 }}>
                  {String(qi.aph) === String(aphRefs.tYield)
                    ? <span>County T-yield <b style={{ color:"#3d5a3d" }}>{aphRefs.tYield}</b></span>
                    : <span>Yours &middot; county T-yield <b style={{ color:"#3d5a3d" }}>{aphRefs.tYield}</b>{" "}
                        <span onClick={() => setQi(s2 => ({ ...s2, aph:String(aphRefs.tYield), aphTouched:true }))}
                          style={{ color:"#2d6a3f", textDecoration:"underline", cursor:"pointer" }}>use</span></span>}
                </div>)}
            </label>
            <label style={{ fontSize:11, color:"#5c6f5c", fontWeight:700 }}>Acres<br/>
              <input type="number" value={qi.acres} placeholder={String(DEFAULT_QUOTE_ACRES)} onChange={e => setQi(s => ({ ...s, acres:e.target.value }))} style={{ marginTop:3, width:80, fontSize:13, padding:"6px 8px", borderRadius:7, border:"1px solid #d5e2d5" }} />
              {blankBasis.source === ACREAGE_SOURCE.ASSUMED && (
                <div style={{ fontSize:10, fontWeight:600, color:"#8a6d1f", marginTop:3 }}>illustrative {DEFAULT_QUOTE_ACRES} ac — a prospect example, not a farm's acreage</div>)}
            </label>
            <label style={{ fontSize:11, color:"#5c6f5c", fontWeight:700, alignSelf:"flex-end", cursor:"pointer" }}>
              <input type="checkbox" checked={aphb.on} onChange={e => setAphb(s => ({ ...s, on:e.target.checked }))} /> Build APH from yield history
            </label>
          </div>
          {aphb.on && (() => {
            const setRow = (i, patch) => setAphb(s => ({ ...s, rows: s.rows.map((r,ix) => ix===i ? { ...r, ...patch } : r) }));
            // Hard stop at 10 — the APH database is the 10 most recent crop years. The engine
            // also caps, so an 11th could never reach the average, but letting someone type one
            // in and then quietly ignoring it is worse than not offering the button.
            const atMax = aphb.rows.length >= APH_MAX_YEARS;
            const addRow = () => setAphb(s => { if (s.rows.length >= APH_MAX_YEARS) return s; const minYr = Math.min(...s.rows.map(r => +r.year || 2027)); return { ...s, rows:[...s.rows, { year:minYr-1, y:"" }] }; });
            const delRow = i => setAphb(s => ({ ...s, rows: s.rows.length>1 ? s.rows.filter((_,ix)=>ix!==i) : s.rows }));
            const excSet = new Set(aphRefs.excludableYears || []);
            const opt = (k, lbl, desc, disabled) => (
              <label title={desc} style={{ fontSize:12, color: disabled ? "#b7c2b7" : "#2a3a2a", fontWeight:600, marginRight:14, cursor: disabled ? "not-allowed" : "pointer" }}>
                <input type="checkbox" disabled={disabled} checked={!!aphb[k]} onChange={e => setAphb(s => ({ ...s, [k]:e.target.checked }))} /> {lbl}
              </label>
            );
            const iCell = { width:64, fontSize:12.5, padding:"4px 6px", borderRadius:6, border:"1px solid #d5e2d5", textAlign:"right" };
            return (
              <div style={{ border:"1px solid #cfe0cf", borderRadius:10, padding:"12px 14px", marginBottom:12, background:"#fbfdfb" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:8, marginBottom:8 }}>
                  <div style={{ fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#60735f", fontWeight:700 }}>APH database — {capCty(qi.county)} wheat</div>
                  <div style={{ fontSize:11, color:"#5c6f5c" }}>
                    T-yield <b>{aphRefs.tYield ?? "—"}</b> · TA factor <b>{aphRefs.taFactor != null ? aphRefs.taFactor.toFixed(2)+" bu/yr" : "—"}</b> · YE years <b>{excSet.size ? [...excSet].join(", ") : "none"}</b>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
                  {aphb.rows.map((r,i) => (
                    <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                      <input type="number" value={r.year} onChange={e => setRow(i, { year:e.target.value })} style={{ ...iCell, width:56, color: excSet.has(+r.year) && aphb.ye ? "#a5461b" : "#2a3a2a" }} />
                      <input type="number" value={r.y} placeholder="bu" onChange={e => setRow(i, { y:e.target.value })} style={iCell} />
                      <button onClick={() => delRow(i)} title="remove" style={{ border:"none", background:"none", color:"#c0392b", cursor:"pointer", fontSize:11, lineHeight:1 }}>✕</button>
                    </div>
                  ))}
                  <button onClick={addRow} disabled={atMax} title={atMax ? "The APH database holds the 10 most recent crop years" : ""} style={{ alignSelf:"flex-start", marginTop:2, fontSize:12, fontWeight:700, padding:"5px 10px", borderRadius:6, border:"1px dashed " + (atMax ? "#d5e2d5" : "#9ec49e"), background:"#fff", color: atMax ? "#b7c2b7" : "#1f7a3d", cursor: atMax ? "not-allowed" : "pointer" }}>+ year</button>{atMax && <span style={{ alignSelf:"center", fontSize:10.5, color:"#677167", marginLeft:6 }}>10-yr max</span>}
                </div>
                <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:2, borderTop:"1px solid #eef3ee", paddingTop:9 }}>
                  <span style={{ fontSize:10.5, textTransform:"uppercase", letterSpacing:".04em", color:"#677167", fontWeight:700, marginRight:8 }}>Options</span>
                  {opt("ta", "TA", "Trend Adjustment — RMA TA-APH factor × years-to-current, per year", aphRefs.taFactor == null)}
                  {opt("ya", "YA", "Yield Adjustment — floors low years at 60% of T-yield", aphRefs.tYield == null)}
                  {opt("yc", "YC", "Yield Cup — approved can't fall below 90% of last year's approved")}
                  {opt("ye", "YE", "Yield Exclusion — drops RMA-designated disaster years", !excSet.size)}
                  {aphb.yc && <span style={{ fontSize:11.5, color:"#5c6f5c", marginLeft:6 }}>prior APH <input type="number" value={aphb.prior} onChange={e => setAphb(s => ({ ...s, prior:e.target.value }))} style={{ width:56, marginLeft:4, ...iCell }} /></span>}
                </div>
                {aphCalc && (
                  <div style={{ marginTop:10, display:"flex", alignItems:"baseline", gap:12, flexWrap:"wrap" }}>
                    <div style={{ fontSize:15 }}><span style={{ fontSize:10.5, textTransform:"uppercase", letterSpacing:".04em", color:"#677167", fontWeight:700, marginRight:6 }}>{aphProvenance({ edited:true }).label}</span><b style={{ color:"#1f5c2e", fontSize:20 }}>{aphCalc.approved ?? "—"}</b> bu</div>
                    <div style={{ fontSize:11, color:"#60735f" }}>
                      {aphCalc.nActual} yr{aphCalc.nActual===1?"":"s"} actual
                      {aphCalc.nDropped ? ` · ${aphCalc.nDropped} beyond 10 yrs ignored` : ""}
                      {aphCalc.nExcluded ? ` · ${aphCalc.nExcluded} excluded` : ""}
                      {aphCalc.nFloored ? ` · ${aphCalc.nFloored} floored (YA)` : ""}
                      {aphCalc.taBu ? ` · +${aphCalc.taBu} TA` : ""}
                      {aphCalc.ycFloored ? " · cupped (YC)" : ""}
                    </div>
                    {aphCalc.estimateReason && <div style={{ fontSize:10.5, color:"#8a6d1f" }}>{aphCalc.estimateReason}</div>}
                    {aphCalc.note && <div style={{ fontSize:10.5, color:"#b8860b" }}>⚠ {aphCalc.note}</div>}
                    <div style={{ fontSize:10.5, color:"#677167" }}>Silas estimate — the AIP's approved yield governs the policy.</div>
                  </div>
                )}
                <div style={{ fontSize:10, color:"#b8860b", marginTop:8 }}>APH engine built from the RMA 2027 ADM (T-yields, trend factors, exclusion years). The MPCI premium math is AIP-validated penny-exact; the approved-yield recompute (TA rounding, &lt;4-year fill, YA/YC sequence) should be confirmed against one AIP illustration that shows the yield grid.</div>
              </div>
            );
          })()}
          <div style={{ display:"flex", gap:14, flexWrap:"wrap", alignItems:"center", marginBottom:12 }}>
            {priceBarOf(qi.practice, qi.state)}
            {[["SCO", "scoPct", qi.sco], ["ECO", "ecoPct", qi.eco]].map(([lbl, key, on]) => (
              <AreaCovPctSlider key={key} label={`${lbl} cov`} pct={qi[key]} on={on}
                                onChange={v => setQi(s => ({ ...s, [key]: v }))} />
            ))}
            {(+qi.scoPct < 100 || +qi.ecoPct < 100) && <span style={{ fontSize:10.5, color:"#677167" }}>reduced election — coverage &amp; premium prorated</span>}
          </div>
          {/* NAU-style single-config builder */}
          {(() => {
            // The price follows the PRACTICE (blankPx = pxFor(practice, state)). A certified-organic
            // quote is not the conventional quote at a different rate — RMA publishes a separate
            // election (2026 wheat: $11.16 vs $5.76), so reading px straight would halve an organic
            // grower's guarantee. MC takes no enterprise discount of its own: once a county
            // qualifies it is rated on its own acres.
            //
            // ⚠ PRICED BY blankQuoteCoverage, THE SAME CALL THE GRID AND THE PRINTED SHEET MAKE.
            // This panel used to price inline, so the quote on screen and the one on paper were
            // two copies of one rule — the class this file has recorded twice on the wheat quoter.
            const bq = blankQuote;
            const q = bq.ok ? bq.q : null, sco = bq.ok ? bq.sco : null, eco = bq.ok ? bq.eco : null;
            const acres = blankBasis.acres;
            const sbtn = a => ({ fontSize:12, fontWeight:700, padding:"5px 11px", borderRadius:6, cursor:"pointer", border:"1px solid "+(a?"#2d6a2d":"#d5e2d5"), background:a?"#eaf3ea":"#fff", color:a?"#1f7a3d":"#5c6f5c" });
            const sl = { fontSize:11, color:"#5c6f5c", fontWeight:700, marginRight:2 };
            const rl = { fontSize:10.5, textTransform:"uppercase", letterSpacing:".04em", color:"#677167", fontWeight:700, marginRight:5 };
            return (
              <div style={{ border:"1px solid #d5e2d5", borderRadius:10, padding:"12px 14px", marginBottom:16, background:"#fbfdfb" }}>
                <div style={{ fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#60735f", fontWeight:700, marginBottom:9 }}>Build one coverage</div>
                <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:8, flexWrap:"wrap" }}>
                  <span style={sl}>Plan</span>
                  {[["RP","RP"],["YP","YP"],["HPE","RP-HPE"]].map(([v,l]) => <button key={v} style={sbtn(qi.plan===v)} onClick={() => setQi(s => ({ ...s, plan:v }))}>{l}</button>)}
                  <span style={{ ...sl, marginLeft:12 }}>Unit</span>
                  {[["OU","Optional"],["BU","Basic"],["EU","Enterprise"]].map(([v,l]) => <button key={v} style={sbtn(qi.unit===v)} onClick={() => setQi(s => ({ ...s, unit:v }))}>{l}</button>)}
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:9, flexWrap:"wrap" }}>
                  <span style={sl}>Level</span>
                  {LEVELS.map(l => <button key={l} style={sbtn(qi.cl===l)} onClick={() => setQi(s => ({ ...s, cl:l }))}>{Math.round(l*100)}%</button>)}
                  <label style={{ fontSize:12.5, color:"#3a4a6a", marginLeft:12 }}><input type="checkbox" checked={qi.sco} onChange={e => setQi(s => ({ ...s, sco:e.target.checked }))} /> SCO→90%</label>
                  <label style={{ fontSize:12.5, color:"#3a4a6a" }}><input type="checkbox" checked={qi.eco} onChange={e => setQi(s => ({ ...s, eco:e.target.checked }))} /> ECO {Math.round(scoEcoBands(MPCI_WHEAT_YEAR).ecoBottom*100)}→{Math.round(scoEcoBands(MPCI_WHEAT_YEAR).ecoTop*100)}%</label>
                </div>
                {/* Policy options. QL carries no premium effect — its ADM option rate is
                    1.0000 in every county of all five states — so it is recorded on the
                    quote rather than priced. TA/YA/YC live in the APH builder because they
                    move the YIELD, not the rate. */}
                <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:9, flexWrap:"wrap" }}>
                  <span style={sl}>Options</span>
                  <label style={{ fontSize:12.5, color:"#3a4a6a" }} title="Beginning or Veteran Farmer & Rancher — +10 percentage points of premium subsidy, and the YA plug rises from 60% to 80% of the T-yield">
                    <input type="checkbox" checked={!!qi.bfr} onChange={e => setQi(s => ({ ...s, bfr:e.target.checked }))} /> BFR/VFR
                  </label>
                  <label style={{ fontSize:12.5, color:"#3a4a6a" }} title="Multi-County Enterprise Unit — lets a county that can not meet the enterprise-unit test on its own qualify by borrowing acreage from another county you farm. It does NOT change the arithmetic: once it qualifies, the county is rated on its own acres. Verified against R&H at 400 and 100 acres.">
                    <input type="checkbox" checked={!!qi.mc} onChange={e => setQi(s => ({ ...s, mc:e.target.checked }))} disabled={qi.unit !== "EU"} /> MC <span style={{ color:"#677167", fontSize:11 }}>(qualifies a small county for EU)</span>
                  </label>
                  {/* PF is not in the 2027 wheat option set (ADM: EI EO HF LP MC QL TA YA YC
                      YE). Shown disabled here as well as on the farm card so a prospect quote
                      offers the same visible set and nobody assumes it was omitted. */}
                  <label style={{ fontSize:12.5, color:"#b7c2b7", cursor:"not-allowed" }} title="Not offered on 2027 fall wheat — expected back for spring 2027">
                    <input type="checkbox" disabled /> PF <span style={{ fontSize:11 }}>(not offered 2027)</span>
                  </label>
                  <label style={{ fontSize:12.5, color:"#3a4a6a" }} title="Quality Loss — recorded on the quote; its ADM option rate is 1.0000, so it does not change the premium">
                    <input type="checkbox" checked={!!qi.ql} onChange={e => setQi(s => ({ ...s, ql:e.target.checked }))} /> QL <span style={{ color:"#677167", fontSize:11 }}>(no premium effect)</span>
                  </label>
                </div>

                {bq.ok ? (
                  <div style={{ display:"flex", gap:22, flexWrap:"wrap", alignItems:"baseline", borderTop:"1px solid #eef3ee", paddingTop:10 }}>
                    <div><span style={rl}>MPCI {BLANK_PLAN_LABEL[qi.plan] || qi.plan}</span><b>{$ac(q.netAcre)}</b>/ac <span style={{ color:"#677167", fontSize:11 }}>· cov {$ac(q.liabPerAc)}</span></div>
                    {sco && <div><span style={rl}>SCO</span><b>{$ac(sco.netAcre)}</b>/ac <span style={{ color:"#677167", fontSize:11 }}>· cov {$ac(sco.covPerAc)}</span></div>}
                    {eco && <div><span style={rl}>ECO</span><b>{$ac(eco.netAcre)}</b>/ac <span style={{ color:"#677167", fontSize:11 }}>· cov {$ac(eco.covPerAc)}</span></div>}
                    <div style={{ fontSize:14 }}><span style={rl}>Total</span><b style={{ color:"#1f5c2e" }}>{$ac(bq.netAc)}/ac</b> <span style={{ color:"#677167", fontSize:12 }}>· ${Math.round(bq.netTotal).toLocaleString()} on {acres} ac{blankBasis.source === ACREAGE_SOURCE.ASSUMED ? " (illustrative)" : ""} · cov {$ac(bq.covAc)}/ac</span></div>
                    {(bq.scoMissing || bq.ecoMissing) && <div style={{ fontSize:11, color:"#a5461b", flexBasis:"100%" }}>{[bq.scoMissing && "SCO", bq.ecoMissing && "ECO"].filter(Boolean).join(" and ")} could not be priced for this county and is not in the total.</div>}
                  </div>
                ) : <div style={{ color:"#a5461b", fontSize:12 }}>{bq.reason}</div>}
                {qi.plan !== "RP" && <div style={{ fontSize:10, color:"#b8860b", marginTop:7 }}>YP / RP-HPE are built from the RMA spec (RP is AIP-validated penny-exact); confirm against one NAU/R&H {qi.plan} quote when convenient.</div>}
              </div>
            );
          })()}
          {/* The grid follows the builder's election (unit, plan, SCO/ECO) and takes every row
              from blankQuoteCoverage, so the quoted level in this table IS the quote above it. */}
          {quoteGrid(qi.state, qi.county, qi.aph, blankBasis.acres, blankPx.price, blankPx.vol, null, qi.scoPct, qi.ecoPct,
            { bfr: !!qi.bfr, practice: qi.practice, unit: qi.unit, sco: !!qi.sco, eco: !!qi.eco,
              planLabel: BLANK_PLAN_LABEL[qi.plan] || qi.plan,
              rowFor: cl => blankQuoteCoverage({ ...blankCfg, cl }, WHEAT_BLANK_ENG) })}
          {(() => {
            // ⚠ AN EXPLICIT EMPTY HISTORY. Left null, recommendWheat falls back to
            // yieldSeries(crop, county) — the production history of whichever farm was last
            // picked in "From a policy", which stays loaded when the agent switches here. So a
            // prospect's recommendation for Orleans could rest on another grower's Orleans
            // yields. A blank quote has no history but the APH typed or built above.
            const rec = recommendWheat(qi.state, qi.county, qi.aph, qi.acres, pxFor(qi.practice, qi.state).price, pxFor(qi.practice, qi.state).vol, null, [], null, null, null, qi.practice);
            if (!rec) return null;
            // ⚠ THE ENGINE CHOOSES THE LEVEL WITH SCO AND ECO AT THE FULL BAND, and its
            // headline $/ac is priced there. With a reduced election on the sliders above,
            // printing that figure would state a premium the quote beside it does not carry.
            // With a reduced election the headline is re-priced through blankQuoteCoverage —
            // the ONE blank-quote pricer, the same call the builder above uses — at the
            // recommended level and unit with both layers on, which is the stack this box
            // names. The box then says the LEVEL was still chosen on the full band.
            const recScoP = areaCoveragePct(qi.scoPct), recEcoP = areaCoveragePct(qi.ecoPct);
            const recReduced = (recScoP != null && recScoP < 100) || (recEcoP != null && recEcoP < 100);
            const recAtElection = recReduced ? blankQuoteCoverage({ ...blankCfg, cl: rec.cl, unit: rec.unit, plan: "RP", sco: true, eco: true }, WHEAT_BLANK_ENG) : null;
            // A premium the engine WITHHELD (no acreage it may price on) stays withheld: a
            // reduced election must not be the route by which one appears.
            const recNetShown = rec.netAc == null ? null : !recReduced ? rec.netAc
              : (recAtElection && recAtElection.ok && !recAtElection.scoMissing && !recAtElection.ecoMissing ? recAtElection.netAc : null);
            return (
              <div style={{ marginTop:16, border:"1px solid #cfe3cf", borderRadius:10, background:"#f4faf4", padding:"12px 14px" }}>
                <div style={{ fontSize:13.5, fontWeight:800, color:"#1f5c2e", marginBottom:4 }}>Our recommendation: {Math.round(rec.cl*100)}% {rec.unit==="EU"?"Enterprise":"Basic"} RP + {areaCoverageLabel("SCO", qi.scoPct)} + {areaCoverageLabel("ECO", qi.ecoPct)} <span style={{ fontWeight:600, color:"#3a6a3a", fontSize:12 }}>· {recNetShown == null ? "—" : $ac(recNetShown)}/ac</span></div>
                <ul style={{ margin:"6px 0 0", paddingLeft:18, fontSize:12, color:"#3a4a3a", lineHeight:1.5 }}>{rec.why.map((w, i) => <li key={i} style={{ marginBottom:3 }}>{w}</li>)}</ul>
                {recReduced && <div style={{ fontSize:11, color:"#60735f", marginTop:4 }}>The level is chosen with SCO and ECO at the full band; the premium above and the table below price the percentages elected on the sliders.</div>}
                {recLadder(qi.state, qi.county, qi.aph, blankBasis.acres, pxFor(qi.practice, qi.state).price, pxFor(qi.practice, qi.state).vol, rec.cl, rec.unit, { bfr: !!qi.bfr, practice: qi.practice, scoPct: qi.scoPct, ecoPct: qi.ecoPct })}
              </div>
            );
          })()}
          <ScoEcoBacktest stateFips={qi.state} county={qi.county} crop={crop} aph={qi.aph} baseCl={qi.cl} scoCovPct={qi.sco ? (+qi.scoPct || 100) : 0} ecoCovPct={qi.eco ? (+qi.ecoPct || 100) : 0} bands={btBands} onBands={setBtBands} />
        </div>
      )}
      {mode === "blank" && crop !== "WHEAT" && <div style={{ ...sect, color:"#677167", fontSize:13 }}>The quoter is live for <b>wheat</b> (2027 pilot). Corn/soybeans plug in once their ADM rate sets are extracted. Switch the crop to Wheat above.</div>}

      {/* farm search / selector */}
      {mode === "farm" && (<>
      <div style={{ ...sect, marginBottom:14 }}>
        <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:6 }}>Select a farm <span style={{ color:"#677167", fontWeight:600, textTransform:"none", letterSpacing:0 }}>({farms.length} farms{q.trim() ? ` · ${matches.length} match${matches.length === 1 ? "" : "es"}` : ""})</span></div>
        <input value={farmer ? farmer.name : q} onChange={e => { setQ(e.target.value); setSel(null); }} placeholder="Type a farm, grower, or policy #…"
          autoComplete="off" autoCorrect="off" spellCheck={false} name="ca-farm-search-x"
          style={{ width:"100%", maxWidth:440, fontSize:14, padding:"9px 12px", borderRadius:8, border:"1px solid #d5e2d5", boxSizing:"border-box" }} />
        {!farmer && q.trim() && matches.length === 0 && <div style={{ marginTop:6, fontSize:12.5, color:"#a5461b" }}>No policyholder matches “{q.trim()}”.</div>}
        {!farmer && matches.length > 0 && (
          <div style={{ marginTop:6, maxWidth:440, border:"1px solid #e4ede4", borderRadius:8, maxHeight:230, overflowY:"auto" }}>
            {matches.map((f, i) => (
              <div key={f.name + "|" + i} onClick={() => { setSel(f); setQ(""); }} style={{ padding:"7px 12px", fontSize:13.5, cursor:"pointer", borderBottom:"1px solid #f2f6f2", display:"flex", justifyContent:"space-between", gap:10 }}>
                <span style={{ fontWeight:600, color:"#1a2e1a" }}>{f.name}</span>
                <span style={{ color:"#677167", fontSize:11 }}>{f.policyNums.length} {f.policyNums.length === 1 ? "policy" : "policies"}</span>
              </div>
            ))}
          </div>
        )}
        {farmer && <div style={{ marginTop:6 }}><button onClick={() => { setSel(null); setQ(""); }} style={{ fontSize:11, padding:"4px 10px", borderRadius:6, border:"1px solid #d5e2d5", background:"#f2f6f2", cursor:"pointer", color:"#5c6f5c" }}>← change farm</button></div>}
        <div style={{ marginTop:10, paddingTop:9, borderTop:"1px solid #eef3ee" }}>
          <button onClick={() => setBatchOpen(o => !o)} style={{ fontSize:12, fontWeight:700, padding:"5px 12px", borderRadius:7, border:"1px solid #2d6a2d", background:batchOpen?"#eaf3ea":"#fff", color:"#1f7a3d", cursor:"pointer" }}>🖨 Batch print quotes {batchOpen ? "▴" : "▾"}</button>
          {batchOpen && (() => {
            const sel = batchSel, selCount = Object.keys(sel).length;
            const s = batchQ.trim().toLowerCase();
            const list = (s ? farms.filter(f => f.name.toLowerCase().includes(s) || f.policyNums.join(" ").toLowerCase().includes(s)) : farms).slice().sort((a,b)=>a.name.localeCompare(b.name)).slice(0, 300);
            const toggle = f => setBatchSel(m => { const n = { ...m }; if (n[f.name]) delete n[f.name]; else n[f.name] = f; return n; });
            return (
              <div style={{ marginTop:8, maxWidth:480 }}>
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:6 }}>
                  <input value={batchQ} onChange={e => setBatchQ(e.target.value)} placeholder="Filter farms…" style={{ flex:"1 1 180px", fontSize:13, padding:"7px 10px", borderRadius:7, border:"1px solid #d5e2d5" }} />
                  <button disabled={!selCount || batchBusy} onClick={() => printWheatBatch(Object.values(sel))} style={{ fontSize:12.5, fontWeight:800, padding:"7px 14px", borderRadius:7, border:"none", background: (!selCount||batchBusy)?"#b7c7b7":"#2d6a2d", color:"#fff", cursor:(!selCount||batchBusy)?"default":"pointer" }}>{batchBusy ? "Building…" : `Print ${selCount||""} selected`}</button>
                  {selCount>0 && <button onClick={() => setBatchSel({})} style={{ fontSize:11.5, padding:"6px 10px", borderRadius:7, border:"1px solid #d5e2d5", background:"#f2f6f2", color:"#5c6f5c", cursor:"pointer" }}>clear</button>}
                </div>
                <div style={{ fontSize:10.5, color:"#677167", marginBottom:5 }}>Check farms to include. Each farm prints its wheat counties (one per page); farms with no wheat are skipped. Prices are live/estimated at print time.</div>
                <div style={{ border:"1px solid #e4ede4", borderRadius:8, maxHeight:260, overflowY:"auto" }}>
                  {list.map((f, i) => (
                    <label key={f.name+"|"+i} style={{ display:"flex", alignItems:"center", gap:9, padding:"6px 11px", fontSize:13, borderBottom:"1px solid #f2f6f2", cursor:"pointer", background: sel[f.name]?"#f4faf4":"#fff" }}>
                      <input type="checkbox" checked={!!sel[f.name]} onChange={() => toggle(f)} />
                      <span style={{ fontWeight:600, color:"#1a2e1a", flex:1 }}>{f.name}</span>
                      <span style={{ color:"#677167", fontSize:11 }}>{f.policyNums.length} pol</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {!farmer && <>
        <CoverageAdvisorDashboard crop={crop} cropYear={MPCI_WHEAT_YEAR - 1} />
        <div style={{ padding:"4px 6px 24px", color:"#677167", fontSize:12.5 }}>Search a farm above to see its {crop.toLowerCase()} picture — every insured unit across counties/policies, APH, years of history, and per-unit risk (CV + basis to county).</div>
      </>}
      {farmer && loading && <div style={{ padding:30, color:"#677167" }}><UiSpinner />Loading {farmer.name}'s coverage…</div>}

      {farmer && !loading && (
        <>
          <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap", marginBottom:10 }}>
            <h2 style={{ margin:0, fontSize:19, fontWeight:800, color:"#1a2e1a" }}>{farmer.name}</h2>
            <span style={{ fontSize:12, color:"#5c6f5c" }}>{farmStates.join(" · ")} · {crop.charAt(0) + crop.slice(1).toLowerCase()} · {farmer.policyNums.join(" · ")}</span>
          </div>

          {(() => {
            const covRows = (byCrop[crop] || []);
            const covCounties = new Set(covRows.map(r => (r.county || "").toUpperCase()));
            // Counties they operate this crop in (production history) but don't insure this year — quotable.
            const opCounties = [...new Set(ph.filter(p => p.crop === crop && p.county).map(p => (p.county || "").toUpperCase()))];
            const extraRows = opCounties.filter(c => !covCounties.has(c)).map(c => {
              const acR = ph.filter(p => p.crop === crop && (p.county || "").toUpperCase() === c && p.acres != null && +p.acres > 0).sort((a,b) => b.crop_year - a.crop_year);
              return { county:c, type:"W", practice:"Non-Irrigated", aph_yield:null, coverage_level:null, plan:null, acres: acR.length ? +acR[0].acres : null, _quoteOnly:true };
            });
            // ONE row per county. covRows spans every crop year the farm holds, so a
            // grower carried on both 2026 and 2027 produced two rows for the same county
            // — Atwater printed Orleans twice, at APH 85 and 86. The current year wins for
            // display; the prior year is kept alongside for the renewal comparison.
            const yrOf = rr => { const y = polYears[String(rr && rr.policy_id)]; return y == null ? null : +y; };
            const CY = MPCI_WHEAT_YEAR, PY = MPCI_WHEAT_YEAR - 1;
            const perCounty = {};
            for (const rr of covRows) {
              const c = (rr.county || "").toUpperCase(); if (!c) continue;
              const g = (perCounty[c] ||= { cur: null, prev: null });
              const y = yrOf(rr);
              if (y === CY) { if (!g.cur) g.cur = rr; }
              else if (y === PY) { if (!g.prev) g.prev = rr; }
              else if (!g.cur) g.cur = rr;        // year unknown — treat as current rather than dropping it
            }
            const endoFor = (c, y, test) => endorsements.find(e => (e.county || "").toUpperCase() === c && yrOf(e) === y && test(e.plan));
            const covRowsDeduped = Object.entries(perCounty).map(([c, g]) => ({ ...(g.cur || g.prev), county: c, _prev: g.prev || null, _curAcres: (g.cur && +g.cur.acres > 0) ? +g.cur.acres : null }));
            const rows = [...covRowsDeduped, ...extraRows].slice().sort((a, b) => (a.county || "").localeCompare(b.county || ""));
            if (!rows.length) {
              const have = Object.keys(byCrop);
              return <div style={{ padding:16, color:"#677167", fontSize:13 }}>No {crop.toLowerCase()} coverage on file for {farmer.name}.{have.length ? " On file: " + have.map(c => c.charAt(0) + c.slice(1).toLowerCase()).join(", ") + " — switch crop above." : ""}</div>;
            }
            const tot = rows.reduce((a, r) => a + (+r.acres || 0), 0);
            return (
              <>
              <div style={sect}>
                <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:8, flexWrap:"wrap" }}>
                  <h3 style={{ margin:0, fontSize:15, color:"#1a2e1a" }}>{crop.charAt(0) + crop.slice(1).toLowerCase()}</h3>
                  <span style={{ fontSize:11.5, color:"#677167" }}>{rows.length} {rows.length === 1 ? "county" : "counties"} · {tot ? tot.toFixed(0) + " ac" : "—"} · across {farmer.policyNums.length} {farmer.policyNums.length === 1 ? "policy" : "policies"}</span>
                </div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", minWidth:640 }}>
                    <thead><tr style={{ textAlign:"left" }}>
                      <th style={th}>County</th><th style={th}>St</th><th style={th}>Type·Prac</th><th style={{ ...th, textAlign:"right" }}>APH</th>
                      <th style={{ ...th, textAlign:"center" }}>Lvl</th><th style={th}>Plan</th><th style={{ ...th, textAlign:"right" }}>Acres</th>
                      <th style={{ ...th, textAlign:"right" }}>Cty Exp</th><th style={{ ...th, textAlign:"right" }}>CV</th><th style={{ ...th, textAlign:"right" }}>Basis</th><th style={th}>APH database</th>
                    </tr></thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const series = yieldSeries(crop, r.county);
                        const cv = cvOf(series);
                        const ce = countyExp(crop, r.county, unitState(r));
                        const aph = r.aph_yield != null ? +r.aph_yield : (series.length ? series.reduce((s, o) => s + o.y, 0) / series.length : null);
                        const basis = (aph != null && ce) ? aph / ce : null;
                        const db = series.slice(-10).slice().sort((a, b) => b.yr - a.yr);   // most-recent 10 yrs, newest first
                        return (
                          <tr key={i} style={{ background: i % 2 ? "#fafcfa" : "#fff" }}>
                            <td style={{ ...td, fontWeight:600 }}>{capCty(r.county)}</td>
                            <td style={{ ...td, color:"#60735f", fontWeight:700, fontSize:11.5 }}>{unitState(r)}</td>
                            <td style={{ ...td, color:"#5c6f5c" }}>{pracLbl(r.type, r.practice)}</td>
                            <td style={{ ...td, textAlign:"right", fontWeight:700 }}>{aph != null ? aph.toFixed(1) : "—"}</td>
                            <td style={{ ...td, textAlign:"center" }}>{r.coverage_level != null ? Math.round(+r.coverage_level * 100) + "%" : "—"}</td>
                            <td style={{ ...td, color:"#5c6f5c" }}>{r.plan || "—"}</td>
                            <td style={{ ...td, textAlign:"right" }}>{r.acres != null ? (+r.acres).toFixed(0) : "—"}</td>
                            <td style={{ ...td, textAlign:"right", color:"#5c6f5c" }}>{ce != null ? ce.toFixed(0) : "—"}</td>
                            <td style={{ ...td, textAlign:"right", fontWeight:800, color:cvColor(cv) }}>{cv != null ? cv.toFixed(0) + "%" : <span style={{ color:"#c3ccc3", fontWeight:500 }}>&lt;3yr</span>}</td>
                            <td style={{ ...td, textAlign:"right", fontWeight:700, color:basisColor(basis) }}>{basis != null ? basis.toFixed(2) + "×" : "—"}</td>
                            <td style={{ ...td, color:"#60735f", fontSize:11.5 }}>
                              {db.length ? (
                                <details>
                                  <summary style={{ cursor:"pointer", color:"#2d6a2d", fontWeight:600, listStyle:"revert" }}>{db.length}-yr ({db[0].yr}–{db[db.length-1].yr})</summary>
                                  <div style={{ marginTop:5, fontVariantNumeric: "tabular-nums", fontSize:11, color:"#5c6f5c", lineHeight:1.6 }}>
                                    {db.map(o => <div key={o.yr}>{o.yr}: <b style={{ color:"#1a2e1a" }}>{o.y.toFixed(1)}</b> bu</div>)}
                                    <div style={{ marginTop:3, paddingTop:3, borderTop:"1px solid #eef3ee", color:"#677167" }}>avg {(db.reduce((s, o) => s + o.y, 0) / db.length).toFixed(1)}</div>
                                  </div>
                                </details>
                              ) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Historical SCO/ECO backtest — per insured county, on THIS grower's APH + level + acres.
                  WHEAT renders this inside the quoter's own History tab; every other crop keeps it here. */}
              {crop !== "WHEAT" && <div style={sect}>
                <h3 style={{ margin:"0 0 2px", fontSize:15, color:"#1a2e1a" }}>Historical SCO/ECO backtest <span style={{ fontSize:11, fontWeight:600, color:"#677167" }}>· what today's {btBandLabel(btBands)} would have paid on {farmer.name.split(" ")[0]}'s data</span></h3>
                <div style={{ fontSize:12, color:"#5c6f5c", marginBottom:4 }}>Per county below — each unit's APH, coverage level, and reported acres, run against every RMA-settled year (2017–). Dollars are estimates of what the county trigger would have paid; not a promise of future indemnity.</div>
                {rows.map((r, i) => {
                  const series = yieldSeries(crop, r.county);
                  const uAph = r.aph_yield != null ? +r.aph_yield : (series.length ? series.reduce((s, o) => s + o.y, 0) / series.length : null);
                  if (uAph == null) return null;
                  const uCl = r.coverage_level != null ? +r.coverage_level : 0.75;
                  const uAc = (r.acres != null && +r.acres > 0) ? +r.acres : null;
                  return <ScoEcoBacktest key={(r.county || "") + "|" + i} stateFips={stFips(unitState(r))} county={(r.county || "").toUpperCase()}
                            crop={crop} aph={uAph} baseCl={uCl} acres={uAc} label={capCty(r.county)} bands={btBands} onBands={setBtBands}
                            scoCovPct={heldAreaCoveragePct(endorsements, r._prev || r, isScoPlan, yrOf)}
                            ecoCovPct={heldAreaCoveragePct(endorsements, r._prev || r, isEcoPlan, yrOf)}
                            covPctControl />;
                })}
              </div>}
              </>
            );
          })()}

          {endorsements.filter(e => (e.crop || "") === crop).length > 0 && (
            <div style={{ ...sect, background:"#f4f7fb", borderColor:"#cdd8ea" }}>
              <div style={{ fontSize:12.5, color:"#3a4a6a" }}><b>Current {crop.toLowerCase()} area endorsements:</b> {endorsements.filter(e => (e.crop || "") === crop).map(e => `${e.plan} ${capCty(e.county)}`.trim()).join(" · ")}</div>
            </div>
          )}

          {/* ── 2027 quoter ($/acre): 2026 actual → 2027 same → our rec, plus full options grid ── */}
          {crop === "WHEAT" && (() => {
            // The CONVENTIONAL price. Each unit converts it for its own practice via pxFor(),
            // so an organic unit is never quoted off the conventional election.
            const basePrice = +px.price, baseVol = +px.vol;
            // ONE UNIT PER COUNTY. Same crop-year collapse the section above already does —
            // this list did not, so a grower carried on both years rendered his county twice
            // with two different APHs. Meisenzahl Farms holds Ontario at APH 64 on the -26
            // policy and 65 on the -27, and both passed the aph_yield filter.
            //
            // The current year wins and the prior year rides along as _prev for the 2026
            // column. The two APHs are NOT averaged — they belong to different crop years,
            // and blending them would invent a yield that is on no policy anywhere.
            const yrOfU = rr => { const y = polYears[String(rr && rr.policy_id)]; return y == null ? null : +y; };
            const CYU = MPCI_WHEAT_YEAR, PYU = MPCI_WHEAT_YEAR - 1;
            // Grouped by (county x PRACTICE), not by county — see wheatUnitKey. Conventional and
            // organic wheat in one county are two units with two APHs, two rate tables and two
            // price elections; collapsing them to one county dropped whichever came back second.
            const uByCounty = {};
            for (const rr of (byCrop.WHEAT || []).filter(r => r.aph_yield != null)) {
              const c = (rr.county || "").toUpperCase(); if (!c) continue;
              const pc = practiceCodeOf(rr), k = wheatUnitKey(rr);   // one key definition, not a third copy
              const g = (uByCounty[k] ||= { cur:null, prev:null, county:c, prac:pc });
              const y = yrOfU(rr);
              if (y === CYU) { if (!g.cur) g.cur = rr; }
              else if (y === PYU) { if (!g.prev) g.prev = rr; }
              else if (!g.cur) g.cur = rr;
            }
            // practice_code is stamped explicitly so wheatUnitKey(unit) reproduces the grouping
            // key even on a conventional row that carries none in the database.
            const baseUnits = Object.values(uByCounty).map(g => ({ ...(g.cur || g.prev), county: g.county, practice_code: g.prac, _prev: g.prev || null, _curAcres: (g.cur && +g.cur.acres > 0) ? +g.cur.acres : null }));
            const covered = new Set(baseUnits.map(r => (r.county || "").toUpperCase()));
            const wheatPid = baseUnits[0] ? baseUnits[0].policy_id : null;
            // Quote EVERY county they operate wheat in — add counties with wheat production history but
            // no current coverage, so we can price coverage they don't carry this year (APH from history).
            const phCounties = [...new Set(ph.filter(p => p.crop === "WHEAT" && p.county).map(p => (p.county || "").toUpperCase()))];
            const extraUnits = phCounties.filter(c => !covered.has(c)).map(c => {
              const series = yieldSeries("WHEAT", c);
              if (!series.length) return null;
              const calc = computeApprovedYield2027({ state: stFips(unitState({ policy_id: wheatPid })), county: c, yields: series.map(o => ({ year:o.yr, yield:o.y })) });
              if (!calc || calc.approved == null) return null;
              const acRows = ph.filter(p => p.crop === "WHEAT" && (p.county || "").toUpperCase() === c && p.acres != null && +p.acres > 0).sort((a,b) => b.crop_year - a.crop_year);
              return { county:c, crop:"WHEAT", plan:"RP", type:"W", practice:"Non-Irrigated", aph_yield:calc.approved,
                acres: acRows.length ? +acRows[0].acres : null, coverage_level:0.75, unit_structure:"EU", policy_id:wheatPid, _quoteOnly:true, _curAcres:null };
            }).filter(Boolean);
            const units = [...baseUnits, ...extraUnits];
            if (!units.length) return null;
            const unitLbl = { OU:"Optional", BU:"Basic", EU:"Enterprise" };
            const td2 = { padding:"6px 8px", fontSize:12.5, borderTop:"1px solid #eef3ee", textAlign:"right" };
            const colHead = { padding:"6px 8px", fontSize:11, fontWeight:800, textAlign:"right" };
            const perAc = (net, acres) => net != null && acres ? net / acres : null;
            // The engine is validated book-wide against premiums the AIPs actually billed
            // (tools/mpci-quoter/backtest_book.mjs) — every county's rate tables are exact. The
            // only advisory left is a big trend adjustment that pushes the effective rating index
            // past the top ADM level: coverage + guarantee stay exact, premium within ~1% (over).
            const highTa = wheatHighTaCounties(cov, polStates);
            return (
              <div style={sect}>
                <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:10 }}>
                  <h3 style={{ margin:0, fontSize:15, color:"#1a2e1a" }}>2027 Wheat Quote <span style={{ fontSize:11, fontWeight:600, color:"#1f7a3d" }}>AIP-validated · $/acre</span></h3>
                  {/* The standard report is four pages. The appendix adds every table behind it
                      — premium ladders, the year-by-year trigger record, the FSA estimate and the
                      futures levels — and is off unless asked for. Stored per farm (BUG-078). */}
                  <label title="Adds the complete premium tables, trigger record, FSA and futures pages, and the engine's own reasoning after the four-page report"
                    style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:5, fontSize:11.5, fontWeight:700, color: printSec.appendix ? "#1f7a3d" : "#60735f", cursor:"pointer" }}>
                    <input type="checkbox" checked={!!printSec.appendix} onChange={() => setPrintSec(sx => ({ ...sx, appendix: !sx.appendix }))} />
                    Detailed appendix
                  </label>
                  <button onClick={() => printFullStrategyReport()} disabled={reportBusy} title="The four-page coverage strategy a grower can read in two minutes" style={{ fontSize:12, fontWeight:800, padding:"6px 14px", borderRadius:7, border:"1px solid #1a2e1a", background: reportBusy ? "#b7c7b7" : "#1a2e1a", color:"#fff", cursor: reportBusy ? "not-allowed" : "pointer" }}>{reportBusy ? "Building…" : "📦 Full Strategy Report"}</button>
                  <button onClick={printWheatQuote} style={{ fontSize:12, fontWeight:700, padding:"6px 14px", borderRadius:7, border:"1px solid #2d6a2d", background:"#2d6a2d", color:"#fff", cursor:"pointer" }}>🖨 Print quote</button>
                </div>
                {/* ── WHAT THE PRINTED DOCUMENT CARRIES ──────────────────────────────────
                    Kevin, 2026-09-15: "make it so that I can check or uncheck the compare from
                    previous year (but make that the default) … Make it easy for me to easily
                    pick what I want displayed."
                    Always visible rather than behind a menu: a print setting you have to go
                    looking for is one you find out about after printing. Every section starts
                    ON, so doing nothing prints the document that printed before. */}
                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:12,
                              border:"1px solid #e4ede4", borderRadius:9, padding:"7px 10px", background:"#fbfdfb" }}>
                  <span style={{ fontSize:10, fontWeight:800, letterSpacing:".06em", color:"#60735f", marginRight:2 }}>PRINT</span>
                  {[["lastYear", "Last year", `The ${MPCI_WHEAT_YEAR - 1} column on the quote page, and last year's stack in the comparison`],
                    ["stacks", "Coverage stacks", "The bar comparison. Which levels it draws is per unit — set them on the Stacks row in each unit's coverage panel"],
                    ["recPanel", "Recommendation summary", "The green panel: what you pay, coverage, subsidy, guarantee and the drivers behind the pick"],
                    ["comparePage", `${MPCI_WHEAT_YEAR - 1} → ${MPCI_WHEAT_YEAR} page`, "The side-by-side page: price, guarantee, coverage, premium and prevent plant, both years"],
                    ["triggerPage", "Trigger history", "The county's settled SCO/ECO record — which bands it shows is set on the Trigger history tab"],
                  ].map(([k, label, tip]) => (
                    <button key={k} onClick={() => setPrintSec(s => ({ ...s, [k]: !s[k] }))} title={tip}
                      style={{ fontSize:11.5, fontWeight:700, padding:"4px 10px", borderRadius:6, cursor:"pointer",
                               border:"1px solid " + (printSec[k] ? "#2d6a2d" : "#d5e2d5"),
                               background: printSec[k] ? "#eaf3ea" : "#fff", color: printSec[k] ? "#1f7a3d" : "#677167" }}>
                      {printSec[k] ? "✓ " : ""}{label}
                    </button>
                  ))}
                  {/* A document with nothing on it is a mistake, not a setting — and the two
                      pages that can vanish entirely are the ones worth naming. */}
                  {!printSec.lastYear && printSec.comparePage && (
                    <span style={{ fontSize:10.5, color:"#8a6d1f" }}>the {MPCI_WHEAT_YEAR - 1} → {MPCI_WHEAT_YEAR} page needs last year — it will not print</span>
                  )}
                </div>
                {highTa.length > 0 && (
                  <div style={{ marginBottom:12, border:"1px solid #cddcea", background:"#f5f8fc", borderRadius:9, padding:"9px 12px" }}>
                    <div style={{ fontSize:12, color:"#3a5570", lineHeight:1.5 }}>
                      <b style={{ color:"#2a5a8a" }}>ⓘ Premium formula not stated for {highTa.join(", ")}.</b> This grower's trend-adjusted APH pushes the effective rating index above the top of RMA's rate table, where RMA prices it one of two ways (P11-1 §14) depending on whether QL, YE or YC applies to the APH database — and the AIP's file does not say for this database. The premium shown uses the <b>higher</b> formula; the other is up to about 1% lower. <b>Guarantee and liability are exact</b> either way.
                    </div>
                  </div>
                )}
                <div style={{ marginBottom:14 }}>{(() => {
                  const activeUnit = units.find(x => wheatUnitKey(x) === countyTab) || units[0] || null;
                  return priceBarOf((activeUnit || {}).practice_code || "003", activeUnit ? unitState(activeUnit) : null);
                })()}</div>
                {(() => {
                  // SEED THE OPTIONS FROM WHAT HE ALREADY CARRIES on the active county, then
                  // leave them alone. A renewal conversation starts from his current election,
                  // so opening the card with everything off misrepresents the policy and makes
                  // the agent re-tick what is already true. Prefers the current year's string
                  // and falls back to the prior year's; re-seeds when the county tab changes,
                  // and never re-runs for a county already seeded, so an edit survives renders.
                  const seedU = units.find(x => wheatUnitKey(x) === countyTab) || units[0];
                  // The SEED marker is scoped too, or the guard below never re-runs for
                  // the next grower in the same county and he silently inherits the
                  // first one’s ticks instead of being seeded from his own elections.
                  const seedKey = seedU ? wheatOvrKey(seedU) : null;
                  if (seedKey && optSeeded !== seedKey) {
                    const str = String((seedU.insurance_options || (seedU._prev && seedU._prev.insurance_options)) || "").toUpperCase();
                    const has = k => new RegExp(k).test(str);
                    setOptSeeded(seedKey);
                    setFarmOpts(m => ({ ...m, [seedKey]: { ta:has("TA"), ya:has("YA"), yc:has("YC"), ye:has("YE"), mc:has("MC"), pf:has("PF") } }));
                  }
                  // Options are RENDERED in the coverage panel inside the unit card — this
                  // block only seeds them. It used to draw a farm-level options bar above the
                  // county tabs, which was one of four places a coverage could be changed.
                  return null;
                })()}
                {(() => {
                  // UNIT TABS. One unit at a time, plus a History tab holding the SCO/ECO
                  // trigger backtests. Stacked, a three-county farm pushed the second and
                  // third recommendations below a screenful of the first.
                  //
                  // A tab is a (county x practice) unit, and the practice only shows in the
                  // label when that county carries more than one — so the ordinary
                  // conventional-only farm reads exactly as it did, and Jeffres reads
                  // "Wyoming · Non-Irr" beside "Wyoming · Organic" instead of one Wyoming tab
                  // that quietly hid half his wheat.
                  const nPrac = {};
                  for (const r of units) { const c = (r.county||"").toUpperCase(); (nPrac[c] || (nPrac[c] = new Set())).add(practiceCodeOf(r)); }
                  const tabs = units.map(r => {
                    const c = (r.county || "").toUpperCase(), pc = practiceCodeOf(r);
                    const short = { "003":"Non-Irr", "002":"Irrigated", "713":"Organic", "714":"Organic (Trans)" }[pc] || pc;
                    return { key: wheatUnitKey(r), label: capCty(c) + ((nPrac[c] || new Set()).size > 1 ? " · " + short : ""), organic: pc === "713" || pc === "714" };
                  }).filter(t => t.key);
                  const active = countyTab === "__history" ? "__history"
                               : (tabs.some(t => t.key === countyTab) ? countyTab : (tabs[0] && tabs[0].key));
                  const tb = (on, org) => ({ fontSize:12.5, fontWeight:800, padding:"6px 13px", borderRadius:8, cursor:"pointer",
                                      border:"1px solid " + (on ? (org ? "#7a5a10" : "#2d6a2d") : "#d5e2d5"),
                                      background: on ? (org ? "#7a5a10" : "#2d6a2d") : "#fff", color: on ? "#fff" : "#5c6f5c" });
                  return (
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", marginBottom:12 }}>
                      {tabs.map(t => (
                        <button key={t.key} onClick={() => setCountyTab(t.key)} style={tb(active === t.key, t.organic)}>{t.label}</button>
                      ))}
                      {tabs.length > 0 && <span style={{ width:1, height:20, background:"#dde8dd", margin:"0 4px" }} />}
                      <button onClick={() => setCountyTab("__history")} style={tb(active === "__history", false)}>
                        Trigger history
                      </button>
                      {/* ⚠ "printing covers all of them" IS THE HALF THAT MISLEADS. Kevin,
                          2026-09-14: "I unchecked SCO but its still showing when I print" — he
                          unticked SCO on Orleans and ATWATER's NIAGARA page still quoted it,
                          because coverage is set PER UNIT (as RMA elects it: SCO/ECO are per
                          crop per county) while the document covers every unit. The line said
                          what the print covers and never that each unit carries its own. */}
                      {active !== "__history" && tabs.length > 1 && (
                        <span style={{ fontSize:10.5, color:"#677167", marginLeft:2 }}>{tabs.length} units · printing covers all of them, each at its own coverage</span>
                      )}
                    </div>
                  );
                })()}
                {countyTab === "__history" && (
                  <div>
                    <div style={{ fontSize:12, color:"#5c6f5c", marginBottom:8 }}>
                      What today’s {btBandLabel(btBands)} would have paid, per county, on this grower’s own APH and level against every RMA-settled year.
                      {" "}RMA’s settled county revenue starts at <b>2017</b> — the first year its SCO/ECO report publishes — so that is the depth of every table below.
                    </div>
                    {units.map((r, i) => {
                      const bPrac = practiceCodeOf(r);
                      const series = yieldSeries("WHEAT", (r.county || "").toUpperCase(), bPrac);
                      const uAph = r.aph_yield != null ? +r.aph_yield : (series.length ? series.reduce((s2, o) => s2 + o.y, 0) / series.length : null);
                      if (uAph == null) return null;
                      const uCl = r.coverage_level != null ? +r.coverage_level : 0.75;
                      // SCO/ECO trigger on the COUNTY index, which RMA publishes per practice —
                      // so an organic unit backtests against the organic area yield, and the
                      // label says which one you are looking at.
                      const hYear = rr => { const y = polYears[String(rr && rr.policy_id)]; return y == null ? null : +y; };
                      return <ScoEcoBacktest key={wheatUnitKey(r) + "|" + i} stateFips={stFips(unitState(r))}
                                county={(r.county || "").toUpperCase()} crop="WHEAT" aph={uAph} baseCl={uCl}
                                bands={btBands} onBands={setBtBands}
                                scoCovPct={heldAreaCoveragePct(endorsements, r._prev || r, isScoPlan, hYear)}
                                ecoCovPct={heldAreaCoveragePct(endorsements, r._prev || r, isEcoPlan, hYear)}
                                covPctControl
                                label={capCty(r.county) + (bPrac === "003" ? "" : " · " + (WHEAT_PRACTICE_NAMES[bPrac] || bPrac))} />;
                    })}
                  </div>
                )}
                {units.filter(r => countyTab !== "__history" && (() => {
                  const keys = units.map(wheatUnitKey);
                  const active = keys.includes(countyTab) ? countyTab : keys[0];
                  return wheatUnitKey(r) === active;
                })()).map((r, ui) => {
                  const county = (r.county || "").toUpperCase(), storedAph = +r.aph_yield;
                  // Reported acres: the current-year row has none until acreage reporting
                  // (fall 2027 closes Nov 15), so last year’s reported figure stands in. Acres
                  // are a RATING input here — the enterprise discount is banded by them — not a
                  // quantity we quote; every figure on this card is per acre.
                  const reportedAcres = +r.acres || +((r._prev && r._prev.acres) || 0) || 0;
                  // Elected-but-unreported coverage (a dormant-county election the sync surfaces with
                  // null acres/premium) has no acres to quote against. Fall back to the grower's most
                  // recent PLANTED acres so we can still quote what the elected coverage would cost.
                  const histRef = wheatHistAcres(ph, county);
                  const histAcres = histRef.acres;
                  const isElected = !r._quoteOnly && reportedAcres === 0 && r.net_premium == null && r.aph_yield != null;
                  // ACRES WE CAN SOURCE — reported this year, else last year's reported, else the
                  // grower's most recent PLANTED acres. This is the only figure the backward-looking
                  // "2026 actual" column may divide by: dividing a real billed premium by a planned
                  // acreage would invent a $/ac that was never charged.
                  const knownAcres = reportedAcres || histAcres;
                  // ACRES A HUMAN TYPED, for a unit we can source none for. See `plannedAc`.
                  // wheatUnitKey(r), not `uKey` — that const is declared further down and reading it
                  // here is a temporal dead zone throw at render, not a compile error.
                  // ESTIMATED PLANTED ACRES PER DATABASE (Mike, 2026-09-18) outrank the county total:
                  // their sum IS the planting estimate, so it prices the enterprise band too. Same rule
                  // the printed sheet reads (plannedUnitAcres, via resolveUnitAph), stated here because
                  // the acreage is resolved before the APH is.
                  const unitPracEarly = pracOv[wheatOvrKey(r)] || practiceCodeOf(r);
                  const unitPlanSum = plannedUnitAcres(covUnits, polYearOf, { county, practice: unitPracEarly,
                    curYear: polYearOf(r), planned: unitAc[county + "|" + unitPracEarly] }) || 0;
                  const plannedAcres = unitPlanSum > 0 ? unitPlanSum
                    : (+(plannedAc[wheatPlannedKey(r)] || 0) > 0 ? +plannedAc[wheatPlannedKey(r)] : 0);
                  // ⚠ PRIOR-YEAR ACREAGE IS REFERENCE, NEVER A QUOTE BASIS.
                  // `knownAcres` folds in last year's reported figure, which is right for
                  // the backward-looking "actual" column below (a billed premium divides by
                  // the acres it was billed on) and wrong here: last year's ground does not
                  // price this year's premium, and a farm that halves its wheat gets a
                  // correct-looking number computed on land it did not plant. Measured
                  // 2026-09-01: CY2027 wheat has reported acreage on ZERO of 333 coverage
                  // rows and a prior-year figure for 312 of them, so this was not an edge.
                  const reportedThisYear = wheatQuotedYearAcres(r);   // the QUOTED crop year alone
                  const priorAcres = +((r._prev && r._prev.acres) || 0) || histAcres || 0;
                  // ⚠ THE CARD OPENS ON A STATED ASSUMPTION RATHER THAN ON DASHES.
                  // Kevin, 2026-09-01: "have quoter default to 100 acres or something so it
                  // doesn't open blank" — and a card of dashes does read as a broken page,
                  // which is the Metzler failure running the other way.
                  //
                  // What is NOT restored is the SILENT default this replaced. The assumption
                  // ranks below every real figure, so a reported or typed acreage always wins;
                  // the input is prefilled and editable, and typing over it turns the basis
                  // from ASSUMED into PLANNED. Every surface that prints a dollar computed
                  // from the assumption says so — that is the whole difference.
                  // ⚠ THE SAME LADDER THE PRINT PATH USES, and it must stay the same one.
                  // This read `reported || planned || 100` while the printed sheet resolved
                  // last year's acreage, so one farm had two premiums depending on which
                  // surface you opened — Kast $14.95 on screen against $14.63 on paper.
                  // Prior year outranks the assumed default because it is the grower's own
                  // reported figure; both are disclosed, which is what makes either usable.
                  const acreBasis = resolveAcreageBasis({
                    reportedAcres: reportedThisYear,
                    plannedAcres,
                    priorYearAcres: priorAcres,
                    priorYear: +((r._prev && r._prev.acres) || 0) > 0 ? MPCI_WHEAT_YEAR - 1 : histRef.year,
                    assumedAcres: DEFAULT_QUOTE_ACRES,
                  });
                  const acres = acreBasis.acres;
                  // ASSUMED stays the narrow flag it was — the INVENTED figure, not merely
                  // 'not reported this year'. The prior-year rung is a real number the
                  // grower filed, and conflating the two would make the amber warning cry
                  // wolf on every farm with history.
                  const acreageAssumed = acreBasis.source === ACREAGE_SOURCE.ASSUMED;
                  const acreagePrior = acreBasis.source === ACREAGE_SOURCE.PRIOR_YEAR;
                  // No acreage from either side. The guarantee is still exact (acre-invariant), so it
                  // is quoted on a nominal acre and the PREMIUM alone is withheld — `premiumBasis`
                  // is what every cost cell below is gated on, and it is false until acres exist.
                  // ⚠ BUG-088 — A UNIT STRUCTURE NOBODY STATED, AND THE SURFACES SPLIT ON PURPOSE.
                  // `normUnit` turns a blank unit_structure into "EU", the cheapest structure RMA
                  // sells, so a county with nothing on file was priced at enterprise rates with
                  // nothing saying the structure had been invented.
                  //
                  // Measured 2026-09-18 before choosing: 80 of 127 CY2027 wheat units across 57
                  // farms have NO unit structure on any coverage row or acreage record, and CY2026
                  // is 84 of 209 — so this is the ordinary state of the book in quoting season,
                  // not a handful of stragglers.
                  //
                  // So the CUSTOMER DOCUMENTS refuse and the AGENT'S SCREEN discloses. The
                  // strategy report withholds that county's premium and the printed appendix now
                  // does too — a document a grower signs may not carry a rate structure nobody
                  // chose. The card is the agent's working tool and keeps quoting, with the
                  // assumption named beside the figure (`unitAssumed` below), because withholding
                  // on two thirds of the book would leave nothing to quote with.
                  //
                  // Unit structure enters RMA's rating in exactly one place — discFactor's BU/EU
                  // discount band — so the guarantee and the liability are unit-invariant and are
                  // exact either way. Only the premium moves.
                  // An invented acreage prices nothing for a customer (2026-09-19): premium Pending.
                  const premiumBasis = acres > 0 && !acreageAssumed;
                  const quoteAcres = acres || 1;
                  const fips = stFips(unitState(r));   // this unit's state from its policy (handles farms spanning states)
                  // ── PRACTICE ─────────────────────────────────────────────────────────
                  // Per UNIT, not per farm: a grower can farm organic ground in one county
                  // and conventional in the next — or in the SAME county, which all five of
                  // the book's organic wheat growers do. RMA rates AND prices them
                  // separately, so this drives the rate tables, the T-yield, the price
                  // election, and every piece of per-unit state below (options, APH edits,
                  // pinned bars, the recommendation column). Declared here, above its first
                  // use, because every one of those lookups is keyed by it.
                  const uKey = wheatUnitKey(r);
                  // `uKey` stays farm-agnostic: it is the COUNTY TAB's identity within one
                  // farm's card. `oKey` is where the agent's own choices live, and it
                  // carries the policy so they cannot reach another grower — see
                  // wheatOvrKey.
                  const oKey = wheatOvrKey(r);
                  const uPrac = pracOv[oKey] || practiceCodeOf(r);
                  const uPx = pxFor(uPrac, unitState(r));
                  const price = uPrac === "003" ? basePrice : uPx.price;
                  const vol   = uPrac === "003" ? baseVol   : uPx.vol;
                  const fo = farmOpts[oKey] || FARM_OPTS_OFF;
                  // Prevented-planting factor for THIS county+practice, straight from the ADM.
                  // `pfOffered` is data, not a flag: RMA publishes a second A01220 row carrying
                  // option PF when the buy-up is sold, and 2027 fall wheat has none.
                  const ppF = wheatPpFactor2027(fips, county, uPrac, !!fo.pf);
                  // ── APH VINTAGE ───────────────────────────────────────────────────────
                  // Which crop years the approved yield is actually built on, and whether the
                  // most recent completed one is in it. The APH database is a rolling TEN
                  // years: when last year is keyed it goes on and the oldest drops off, which
                  // moves the guarantee. Until then we quote on a database that ends a year
                  // early, and nothing on the page said so.
                  //
                  // That is nearly the whole book right now: 7 of 201 CY2026 wheat production
                  // rows carry a yield, and 99 of 213 units have no current-year coverage row
                  // at all so they quote on last year's approved yield outright. Meisenzahl
                  // Monroe is keyed record-type A with 184.78 acres and a NULL yield — the
                  // acreage is in, the production is not.
                  // WHOSE APPROVED YIELD IS THIS? The AIP re-approves every crop year, and the
                  // roll happens at the AIP, not here: Koch Libra's Livingston wheat went 75 to
                  // 72 when his 2026 claim year (24.5 bu/ac, production_status M) landed on the
                  // database and the oldest year fell off. Where a current-year row exists we
                  // quote that number — 114 of 213 wheat units, and the AIP had moved 80 of
                  // them. Where it does not, we are on LAST YEAR'S approved yield and the page
                  // has to say so, because there is nothing in the data to compute a current
                  // one from: of the 99 units with no current-year row, ZERO had a prior-year
                  // production yield on file when this was written. Claim units get there first
                  // because an adjuster establishes the yield, but the AGENCY gathers and keys
                  // production reports too (Kevin, 2026-08-07) — so those 99 fill in over the
                  // reporting season, and the quote has to pick each one up as it lands.
                  //
                  // ── THE APH THIS CARD PRICES ON — resolveUnitAph, NEVER A LOCAL COPY ──
                  // The whole ladder (agent edit > ticked databases > acre-weighted databases >
                  // rolled forward > stored summary) and all three yields come from the ONE
                  // resolver every printed page also calls. It lived inline here until
                  // 2026-09-18, and the print builder — which never had it — priced Clark Five's
                  // Huntingdon wheat on 75.0 while this card priced 98.7. Everything downstream
                  // reads `aph` / `rateY` / `adjY`, so a ticked database still re-prices the
                  // columns, the bars and the economics panel; acres stay the enterprise's,
                  // because they set the discount band.
                  const ed = aphEdit[uKey];
                  const { aphCropYear, aphIsPriorYear, aphYrs, aphNewest, aphStale, rolled, edCalc, wtd, selBlend,
                          aph, edited, derive, rateY, adjY, quoteRP } = resolveUnitAph(r, { units: covUnits, years: polYears, phRows: ph,
                    fips, prac: uPrac, fo, edit: ed, sel: unitSel[county + "|" + uPrac], planned: unitAc[county + "|" + uPrac] });
                  // CATASTROPHIC COVERAGE. CAT is 50% of the yield at 55% of the price, and its
                  // premium is 100% subsidized — the grower pays a flat administrative FEE, not a
                  // rated premium (Kevin, 2026-08-06). Marquart Bros carries it on wheat in five
                  // counties for CY2027. Quoting it as a buy-up would have shown roughly double the
                  // coverage he owns AND a premium he will never be billed.
                  // The discriminator is the 50/55 pair, not the plan code: code 01 is the yield
                  // plan generally, and a 01 grower at 65% buy-up is not CAT.
                  const uPePct = (r.price_election_pct != null && +r.price_election_pct > 0 && +r.price_election_pct <= 1) ? +r.price_election_pct : 1;
                  const isCat = Math.abs(uPePct - 0.55) < 1e-6 && Math.abs((r.coverage_level != null ? +r.coverage_level : 0) - 0.50) < 1e-6;
                  const inSet =wheatCountyInSet(fips, county, uPrac) && +price > 0;
                  const cardUnitSrc = wheatUnitSource(r, r._prev || null, aipAcre, polYears, polNumbers, MPCI_WHEAT_YEAR);
                  const curCl = r.coverage_level != null ? +r.coverage_level : 0.75, curUnit = wheatQuoteUnit(unitEntry[wheatPlannedKey(r)]);
                  // ⚠ DECLARED HERE, WITH `cardUnitSrc`, NOT UP BESIDE `premiumBasis`. It was written
                  // there first and check-tdz caught it: `cardUnitSrc` is declared 160 lines below
                  // that point, so the card compiled and would have thrown at render.
                  // The agent's own statement, where the file has none. It is NOT a fallback
                  // for a structure the file states — the AIP's own value always wins.
                  // Stated by the agent, or the Enterprise default. cardUnitSrc no longer gates
                  // this — it is the DISPLAY of what the policy is on, not the quote basis.
                  const unitStated = unitEntry[wheatPlannedKey(r)] || null;
                  const unitAssumed = !unitStated;
                  // Penny-exact AIP-quoted 2026 premiums (sync-safe overrides) — used for the "2026 actual"
                  // column on elected/dormant coverage the engine can't derive to the cent (split rate-yield).
                  const ovRows = ovr.filter(o => (o.county||"").toUpperCase() === county && o.policy_number === (polNumbers[String(r.policy_id)] || ""));
                  const ovRP = ovRows.find(o => o.plan === "RP" && Math.abs(+o.coverage_level - curCl) < 1e-6);
                  const ovSco = ovRows.find(o => o.plan === "SCO-RP");
                  const ovEco = ovRows.find(o => o.plan === "ECO-RP");
                  const hasOvr = !!(ovRP || ovSco || ovEco);
                  // 2026 figures come from LAST YEAR’s record, not from `r`. The dedupe above
                  // makes `r` the current-year row, and a 2027 row carries null liability,
                  // premium and acres until reporting — reading them off `r` blanks the whole
                  // “what you paid” column. `_prev` is that county’s prior-year row; it falls
                  // back to `r` for a farm that only has one year on file.
                  const rPrev = r._prev || r;
                  const bfrQ = bfrOptOf(rPrev, r);
                  // The prior-year column's own PP factor, including the buy-up. RMA sold the
                  // `PF` option on 2026 wheat (base 0.600 -> 0.650, per county, A01220) and does
                  // NOT sell it for 2027, so the two columns are a different product and the
                  // election has to be read off the PRIOR row rather than off this year's.
                  // Declared here and not beside ppF because it needs rPrev, which is not yet in
                  // scope up there — check-tdz caught exactly that on the print path's first cut.
                  const prevPp = wheatPpFactorPrior(fips, county, uPrac, rPrev && rPrev.insurance_options);
                  const prevPpF = prevPp.factor != null ? prevPp.factor : getPPFactor('WHEAT');
                  // ── THE DENOMINATOR OF THE BACKWARD-LOOKING COLUMN ──────────────────
                  // Every 2026 figure divides by `prevAcres`, and nothing else may.
                  //   * NOT `acres` — that is the QUOTE basis and falls through to the
                  //     acreage a human typed for a 2027 quote. Dividing a liability the
                  //     AIP actually wrote by a planned figure invents a $/ac that was
                  //     never issued. MPCI already obeyed this; SCO and ECO did not, so
                  //     the two halves of one column could disagree about their own basis.
                  //   * NOT `knownAcres` either, which prefers the CURRENT row's acres:
                  //     the moment the 2027 acreage lands (reporting closes Nov 15) that
                  //     would divide 2026 dollars by 2027 acres. The printed quote has
                  //     always used the prior row's own figure (`pAcres`); this is the
                  //     screen agreeing with it.
                  // Both were LATENT on 2026-09-02 — measured, not assumed: all 130 CY2027
                  // wheat coverages carry null acres, and every CY2026 unit whose base row
                  // has no acres has null liability on its endorsements too, so both halves
                  // blank together today. Latent is not fixed.
                  const prevAcres = (rPrev && +rPrev.acres > 0) ? +rPrev.acres : knownAcres;
                  const curMpci = perAc(rPrev.net_premium != null ? +rPrev.net_premium : null, prevAcres);
                  // Match endorsements to THIS unit by county AND practice — a grower can carry both
                  // conventional and organic wheat in one county (separate RP units); the conventional
                  // ECO/SCO must not leak onto the organic unit (and vice-versa).
                  const uEnd = endorsements.filter(e => (e.crop || "") === "WHEAT" && (e.county || "").toUpperCase() === county && (e.practice || "Non-Irrigated") === (r.practice || "Non-Irrigated"));
                  // Pick the endorsement from the SAME year as the MPCI row feeding this
                  // column. Unfiltered, a farm carried on both years can match the 2027
                  // endorsement — which has no premium yet — and report last year’s SCO/ECO
                  // cost as blank when it was really paid.
                  const prevYr = polYears[String(rPrev.policy_id)];
                  const pickEndo = test => uEnd.find(e => test(e.plan) && polYears[String(e.policy_id)] === prevYr)
                                        || uEnd.find(e => test(e.plan) && e.net_premium != null)
                                        || uEnd.find(e => test(e.plan));
                  const curSco = pickEndo(isScoPlan), curEco = pickEndo(isEcoPlan);
                  // ELECTED SCO/ECO COVERAGE PERCENTAGE. On an endorsement row
                  // price_election_pct is NOT a price election — it is the percentage of the
                  // band the grower bought. Verified against stored liability on every wheat
                  // endorsement that carries one: 0.50 solves to 0.5004, 0.75 to 0.7497, 0.90
                  // to 0.9000. Twelve wheat coverages carry a reduced election, and quoting
                  // them at 100% put roughly DOUBLE the ECO they own in the column that is
                  // supposed to mean 'you changed nothing'.
                  //
                  // ONE RULE NOW (endorsementCoveragePct, src/scoEcoBands.js), shared with the
                  // printed sheet — which quoted every layer at 100% whatever the grower held, so
                  // the screen and the paper disagreed on exactly these twelve-plus growers. A
                  // layer the grower does not carry has no election and takes RMA's 100% default;
                  // a stored value outside RMA's 50–100% is refused (null) and quotes nothing,
                  // where it used to be read as 100%.
                  const endoPct = e => { if (!e) return 1; const p = endorsementCoveragePct(e); return p == null ? null : p / 100; };
                  const curScoPct = endoPct(curSco), curEcoPct = endoPct(curEco);
                  const curScoAc = perAc(curSco && curSco.net_premium != null ? +curSco.net_premium : null, prevAcres);
                  const curEcoAc = perAc(curEco && curEco.net_premium != null ? +curEco.net_premium : null, prevAcres);
                  const curScoCov = curSco ? perAc(curSco.liability, prevAcres) : null;
                  const curEcoCov = curEco ? perAc(curEco.liability, prevAcres) : null;
                  const q = inSet ? quoteRP({ ...bfrQ, state:fips, county, aph, rateYield: rateY, adjustedYield: adjY, cl: curCl, acres: quoteAcres, unit: curUnit, price, vol, practice:uPrac, pf: !!fo.pf, priceElectionPct: uPePct, cat: isCat, plan: isCat ? "YP" : "RP" }) : null;
                  // "2027 · same" mirrors his elected structure: quote SCO/ECO if he carries them today (curSco/curEco)
                  // OR elected them per the AIP override (ovSco/ovEco) even when the endorsement isn't in the DB (dormant).
                  const sSco = inSet && (curSco || ovSco) ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"SCO", baseCl: curCl, aph, acres: quoteAcres, price, vol, practice:uPrac, coveragePct: curScoPct }) : null;
                  const sEco = inSet && (curEco || ovEco) ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"ECO", baseCl: curCl, aph, acres: quoteAcres, price, vol, practice:uPrac, coveragePct: curEcoPct }) : null;
                  // ⚠ THE SAME ARGUMENTS THE PRINTED SHEET PASSES, AND THEY HAVE TO STAY THAT WAY.
                  // This call used to hand the engine `series: null`, `state: null` and no
                  // incumbent, so it saw no yield history and no current election and answered
                  // with its no-evidence benchmark — 80% — on EVERY unit, while the sheet passed
                  // all three and printed the evidenced answer. Kevin, 2026-09-04: "I believe
                  // Silas recommends 80 in Erie but its printing 75." Both were true.
                  // Cripple Creek ERIE is the measured case: 3 usable yield years (2016 126%,
                  // 2019 75%, 2020 58%) against a 75% election. Card 80%, sheet 75%, both classed
                  // INSUFFICIENT_EVIDENCE_TO_CHANGE — so the 80% was never a recommendation, it
                  // was the engine answering with its eyes shut. This is the "rule called SHORT"
                  // class: an omitted optional argument compiles, renders, and is quietly wrong.
                  // The incumbent is read off the PRIOR row exactly as the sheet reads it — `r`
                  // is the quote year and carries no election until the AIP writes one.
                  const uSeries = yieldSeries("WHEAT", county, uPrac);
                  // The farm's own most-recent reported acreage for this county, out of its
                  // APH history — the recommendation's acreage basis when this crop year has
                  // none yet. Same rule the card and the printed quote already display.
                  const cardHistAc = wheatHistAcresFor(county);
                  const cvUnit = cvOf(uSeries);
                  const recPrev = r._prev || null;
                  const recIncCl = (recPrev && recPrev.coverage_level != null) ? +recPrev.coverage_level
                                 : (r.coverage_level != null ? +r.coverage_level : null);
                  // ⚠ A QUOTE HAPPENS BEFORE THERE ARE ACRES. THAT IS WHAT A QUOTE IS.
                  // Kevin, 2026-09-23: *"We have to quote before we will ever have acreage…
                  // that's how it works. We can't wait for acres to be planted and then decide
                  // how to insure it. The optimizer needs to look at historical data not this
                  // years planted acres."*
                  //
                  // This line used to read `inSet && premiumBasis`, and `premiumBasis` is
                  // `acres > 0 && !acreageAssumed`. CY2027 acreage is not reported until
                  // November, so the recommendation was NULL on the entire renewal book —
                  // measured 2026-09-23: **132 of 132 CY2027 units came back ACREAGE_REQUIRED
                  // with zero premiums computed.** It read as an optimizer that never
                  // recommends anything; it was an optimizer that was never asked.
                  //
                  // ⚠ AND THE COLUMN BESIDE IT NEVER HAD THIS GATE. `q` ("2027 · same") quotes
                  // on `quoteAcres` (`acres || 1`) with no premiumBasis test at all, and blanks
                  // only the PREMIUM cell at render (line ~52995). Two columns of one table
                  // answering to two different rules is the whole defect; this makes the
                  // recommendation obey the rule its neighbour already follows.
                  //
                  // ⚠ THE EVIDENCE IS HISTORICAL AND ACRES ARE NOT PART OF IT. Yield records,
                  // area fit, loss frequency and the county trigger record decide the level;
                  // acres enter the arithmetic in exactly one place, `discFactor`'s BU/EU
                  // discount band, and it moves BOTH sides of a marginal comparison together.
                  // MEASURED over 217 units at 50 / 200 / 500 / 1,000 acres — a 20x range:
                  // **216 of 217 recommend the identical coverage level**, one unit moves
                  // (PA-942-6087660 CAMBRIA, 75% below ~500 ac and 80% above). So the level is
                  // acre-invariant in practice and the gate was buying nothing.
                  // The PREMIUM is still withheld until somebody types an acreage — that rule
                  // is untouched and is asserted by check-wheat-quote-acres.
                  const rec = inSet ? recommendWheat(fips, county, aph, acres, price, vol, cvUnit, uSeries, FIPS_STATE[fips], rateY, adjY, uPrac,
                    { incumbent: recIncCl != null ? { level: recIncCl, plan: (recPrev && recPrev.plan) || null,
                        mpciPlanCode: (recPrev && recPrev.mpci_plan_code) || null,
                        priceElectionPct: (recPrev && recPrev.price_election_pct) != null ? +recPrev.price_election_pct : null } : null, unitStructure: cardUnitSrc ? cardUnitSrc.unit : null, unit: curUnit, quoteRP,
                        // The farm's own APH-history acreage, so the comparison can run before
                        // this year's acres are reported. Estimated — it never prices a premium.
                        aphHistoryAcres: cardHistAc.acres || null, aphHistoryYear: cardHistAc.year || null }) : null;
                  const recSeedPct = f => f != null ? f * 100 : AREA_COVERAGE_PCT_DEFAULT;
                  const recDflt = rec ? { cl: rec.cl, unit: rec.unit, sco: !!rec.sco, eco: !!rec.eco, scoPct: recSeedPct(curScoPct), ecoPct: recSeedPct(curEcoPct) }
                                      : { ...REC_DEFAULT, scoPct: recSeedPct(curScoPct), ecoPct: recSeedPct(curEcoPct) };
                  const rc = { ...recDflt, ...(recCfg[uKey] || {}) };   // optimizer seeds the column; user edits override
                  // Same treatment as `q`/`sSco`/`sEco` above: quoted on the nominal acre so the
                  // COVERAGE renders, with the premium blanked at the cell (see the bars below).
                  const rq = inSet ? quoteRP({ ...bfrQ, state:fips, county, aph, rateYield: rateY, adjustedYield: adjY, cl: rc.cl, acres: quoteAcres, unit: rc.unit, price, vol, practice:uPrac, pf: !!fo.pf }) : null;
                  const rSco = inSet && rc.sco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"SCO", baseCl: rc.cl, aph, acres: quoteAcres, price, vol, practice:uPrac, coveragePct: areaCoverageFractionOfPct(rc.scoPct) }) : null;
                  const rEco = inSet && rc.eco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"ECO", baseCl: rc.cl, aph, acres: quoteAcres, price, vol, practice:uPrac, coveragePct: areaCoverageFractionOfPct(rc.ecoPct) }) : null;
                  const setRc = patch => setRecCfg(m => ({ ...m, [uKey]: { ...rc, ...patch } }));
                  // ⚠ AN ENTRY IN recCfg IS NOT AN OVERRIDE. setRc writes the WHOLE of `rc` back,
                  // so the first touch of any control on the unit seeds the key with Silas's own
                  // answer — retick SCO twice and the key exists while nothing has changed. So
                  // "overridden" is a COMPARISON against the recommendation, never a lookup. The
                  // difference is what the sheet's own header says, and a document that reads
                  // "2027 Selected" over a level Silas chose misattributes the decision.
                  const recOverridden = !!rec && (Math.abs(rc.cl - rec.cl) > 1e-9 || rc.unit !== rec.unit
                    || !!rc.sco !== !!rec.sco || !!rc.eco !== !!rec.eco);
                  const recLbl = recOverridden ? "your override" : "Silas rec";
                  // Resetting DELETES the key rather than writing Silas's values into it: a
                  // stored copy of the recommendation stops tracking the engine the moment any
                  // input moves (an APH edit, a practice change), and would then print as an
                  // override of the answer it was copied from.
                  const clearRc = () => setRecCfg(m => { const n = { ...m }; delete n[uKey]; return n; });
                  // ── COVERAGE IS PER UNIT, AND THE DOCUMENT IS PER FARM ────────────────
                  // RMA elects SCO/ECO per crop per COUNTY, so the per-unit tick is right and
                  // stays. What was missing is that an agent editing one tab had no way to see
                  // — or change — what the other units print. Named here, offered as one action.
                  const unitLabelOf = x => {
                    const c = (x.county || "").toUpperCase(), pc = practiceCodeOf(x);
                    const many = units.filter(y => (y.county || "").toUpperCase() === c).length > 1;
                    return capCty(c) + (many ? " · " + ({ "003":"Non-Irr", "002":"Irrigated", "713":"Organic", "714":"Organic (Trans)" }[pc] || pc) : "");
                  };
                  const otherUnits = units.filter(x => wheatUnitKey(x) !== uKey);
                  // Copies THIS unit's whole coverage onto every unit, so the printed document
                  // reads as one decision. Each unit's badge then compares it against that
                  // unit's OWN recommendation, so anywhere it differs still says OVERRIDDEN.
                  const applyCoverageToAllUnits = () => setRecCfg(m => {
                    const n = { ...m };
                    for (const x of units) n[wheatUnitKey(x)] = { cl: rc.cl, unit: rc.unit, sco: rc.sco, eco: rc.eco, scoPct: rc.scoPct, ecoPct: rc.ecoPct };
                    return n;
                  });
                  // The chart's level list, hoisted so BOTH the bars and the grid below use
                  // one source. The grid's LEVELS row is where you reach for "show me that
                  // coverage", so it drives the chart rather than only the grid's own rows.
                  // ── WHICH STACKS THIS UNIT DRAWS, ON SCREEN AND ON PAPER ─────────────
                  // One selection, read by the chart, by the grid's level buttons and by the
                  // printed sheet. Untouched (`undefined`) is AUTO; a tick seeds the selection
                  // from the automatic set and every tick after it adds or removes, so the
                  // agent can print two stacks or six. The quoted level is always drawn.
                  const cSel = barSel[uKey];
                  const autoLv = wheatBarLevels(rc.cl, curCl, LEVELS);
                  const chartLv = wheatChartLevels(rc.cl, curCl, LEVELS, cSel === undefined ? null : cSel);
                  const barsCustom = cSel !== undefined;
                  const toggleChartLevel = cl => toggleBarLevel(uKey, cl, autoLv);
                  const curTot = (curMpci || 0) + (curScoAc || 0) + (curEcoAc || 0);
                  const sameTot = !premiumBasis ? 0 : (q ? q.netAcre : 0) + (sSco ? sSco.netAcre : 0) + (sEco ? sEco.netAcre : 0);
                  // Gated exactly like `sameTot` on the line above. `rq` is no longer null
                  // without an acreage, so the withholding moves here — without it the total
                  // would print a premium rated on the nominal acre, which is the one thing
                  // the acreage rule exists to stop.
                  const recTot = !premiumBasis ? 0 : (rq ? rq.netAcre : 0) + (rSco ? rSco.netAcre : 0) + (rEco ? rEco.netAcre : 0);
                  // Each year column carries a Premium + Coverage pair for MPCI/SCO/ECO/Total, shown side by side.
                  const cTot = (m, s, e) => { const v = (m||0)+(s||0)+(e||0); return v || null; };
                  // Last year's APH = the stored (2026 policy) APH. It equals the 2027 APH until a 2026
                  // production year is added via Update APH — do NOT back-derive it from the guarantee
                  // (round-trip APH→guar→APH drifts 81.0 → 60.8 → 81.1). Price recovered from the policy.
                  const cov2026 = perAc(rPrev.liability, prevAcres);
                  // LAST YEAR'S APH IS LAST YEAR'S, and storedAph is THIS year's the moment the
                  // AIP writes a row — which is most of the renewal season. Kennedy's 2026 column
                  // read "APH 67.4 · 50.6 bu · $4.94/bu": his 2027 approved yield, this year's
                  // guarantee, and a price election back-solved off both that RMA never set. The
                  // real 2026 was 57.8 acre-weighted, 43.4 bu, $5.76 — which is what his own
                  // liability divides to. Prefer the prior year's databases acre-weighted, then
                  // the prior summary row, and only then fall back. Level and unit come from that
                  // row too: 2026 was BASIC and the column was calling it Enterprise.
                  const wtdPrev = weightedAphFor(county, uPrac, aphCropYear - 1);
                  const prevCl = rPrev.coverage_level != null ? +rPrev.coverage_level : curCl;
                  const prevUnit = normUnit(rPrev.unit_structure) || curUnit;
                  const aph2026 = wtdPrev ? wtdPrev.aph
                                : (+rPrev.aph_yield > 0 ? +rPrev.aph_yield
                                : (Number.isFinite(storedAph) ? storedAph : null));
                  // The production guarantee and price come from priorYearChain below — the SAME rule
                  // the printed sheet uses — never APH x level backed into a price.
                  // ⚠ THE SAME THIRD RUNG THE PRINTED SHEET USES — RMA's own published
                  // prior-year election. The first two rungs both need the AIP to have keyed
                  // the policy, and a coverage whose acreage was never keyed has neither a
                  // price election nor a liability to back one out of. That is what left 89
                  // of 131 renewal units with no prior-year bar and the note beside it, and
                  // the bar was already BUILT for this case: `covElect`/`scoElect`/`ecoElect`
                  // derive the layers from `aph2026 * p2026` and `notBilled` already labels
                  // them. It only ever lacked the price. Scaled by the prior row's own price
                  // election, so a CAT unit reads 55% of it.
                  const px26 = pxPrevFor(uPrac, fips);
                  const pePrev = (rPrev.price_election_pct != null && +rPrev.price_election_pct > 0 && +rPrev.price_election_pct <= 1)
                    ? +rPrev.price_election_pct : 1;
                  // ⚠ RMA's published price OUTRANKS the back-solve now (see priorYearChain): the
                  // billed liability over the summary APH x level folded per-database rounding into
                  // the price — $5.77 on Clark Five, where the SOI prints $5.76.
                  const chain2026 = priorYearChain({ storedElection: rPrev.price_election, publishedPrice: px26 ? px26.price : null,
                    priceElectionPct: pePrev, liabPerAc: cov2026, aph: aph2026, cl: prevCl });
                  const p2026 = chain2026.price;
                  const guar2026 = chain2026.guarBu;
                  const YRS = [
                    { lbl: (hasOvr || isElected) ? "2026 elected" : "2026 actual", sub:`${isCat ? "CAT 50/55" : `${Math.round(prevCl*100)}% ${unitLbl[prevUnit]}`}${curScoPct != null && curScoPct < 1 ? ` · ${areaCoverageLabel("SCO", curScoPct*100)}` : ""}${curEcoPct != null && curEcoPct < 1 ? ` · ${areaCoverageLabel("ECO", curEcoPct*100)}` : ""}${hasOvr?" · AIP est":""}${(!hasOvr && !(curTot > 0)) ? " · no acres reported, nothing billed" : ""}`, bg:undefined, col:"#2a3a2a",
                      aphY: aph2026, price: p2026, guar: guar2026, cl: prevCl, rev: cov2026, pe: 1,
                      pp: [null, (cov2026 != null && prevPpF != null) ? cov2026 * prevPpF : null],
                      mpci: hasOvr ? [ovRP?ovRP.net_per_acre:null, ovRP?ovRP.coverage_per_acre:null] : [curMpci, cov2026],
                      sco:  hasOvr ? [ovSco?ovSco.net_per_acre:null, ovSco?ovSco.coverage_per_acre:null] : [curScoAc, curScoCov],
                      eco:  hasOvr ? [ovEco?ovEco.net_per_acre:null, ovEco?ovEco.coverage_per_acre:null] : [curEcoAc, curEcoCov],
                      tot:  hasOvr
                        ? [cTot(ovRP&&ovRP.net_per_acre, ovSco&&ovSco.net_per_acre, ovEco&&ovEco.net_per_acre), cTot(ovRP&&ovRP.coverage_per_acre, ovSco&&ovSco.coverage_per_acre, ovEco&&ovEco.coverage_per_acre)]
                        : [curTot||null, cTot(cov2026, curScoCov, curEcoCov)] },
                    // "fee only" now names the number. CAT premium is 100% subsidised, so the
                    // rated figure beside it is $0 — true, and useless on its own, because the
                    // $655 administrative fee IS the entire cost of CAT to the grower. The fee
                    // is per crop per COUNTY, so it is stated once here rather than added into
                    // a per-acre column that repeats per unit row. (RMA MGR-19-006.)
                    { lbl:"2027 · same", sub:`${isCat ? `CAT 50/55 · fee only${q?.adminFee ? ` · $${q.adminFee}/crop/county` : ""}` : `${Math.round(curCl*100)}% ${unitLbl[curUnit]}`}${curScoPct != null && curScoPct < 1 ? ` · ${areaCoverageLabel("SCO", curScoPct*100)}` : ""}${curEcoPct != null && curEcoPct < 1 ? ` · ${areaCoverageLabel("ECO", curEcoPct*100)}` : ""}`, bg:"#f6faf6", col:"#2a3a2a",
                      aphY: q?aph:null, price: q?+price:null, guar: q?q.guarBu:null, cl: curCl, rev: q?q.liabPerAc:null, pe: q && q.priceElectionPct ? q.priceElectionPct : 1,
                      pp: [null, q?q.ppLiabPerAc:null],
                      mpci:[premiumBasis&&q?q.netAcre:null, q?q.liabPerAc:null], sco:[premiumBasis&&sSco?sSco.netAcre:null, sSco?sSco.covPerAc:null], eco:[premiumBasis&&sEco?sEco.netAcre:null, sEco?sEco.covPerAc:null],
                      tot:[sameTot||null, cTot(q&&q.liabPerAc, sSco&&sSco.covPerAc, sEco&&sEco.covPerAc)] },
                    { lbl:"2027 · " + recLbl, sub:`${Math.round(rc.cl*100)}% ${unitLbl[rc.unit]}${rc.sco?" +"+areaCoverageLabel("SCO", rc.scoPct):""}${rc.eco?" +"+areaCoverageLabel("ECO", rc.ecoPct):""}`, bg:"#eef6ee", col:"#1f5c2e", editable:true,
                      aphY: rq?aph:null, price: rq?+price:null, guar: rq?rq.guarBu:null, cl: rc.cl, rev: rq?rq.liabPerAc:null, pe: 1,
                      pp: [null, rq?rq.ppLiabPerAc:null],
                      mpci:[premiumBasis&&rq?rq.netAcre:null, rq?rq.liabPerAc:null], sco:[premiumBasis&&rSco?rSco.netAcre:null, rSco?rSco.covPerAc:null], eco:[premiumBasis&&rEco?rEco.netAcre:null, rEco?rEco.covPerAc:null],
                      tot:[recTot||null, cTot(rq&&rq.liabPerAc, rSco&&rSco.covPerAc, rEco&&rEco.covPerAc)] },
                  ];
                  const $n = n => n == null ? "—" : $ac(n);
                  // ── THE GUARANTEE CHAIN, the same two rows the printed quote carries ──────
                  // APH x level = production guarantee; production guarantee x price = revenue
                  // guarantee. Read straight off each column's own figures (the engine's guarBu,
                  // the billed liability / published price for last year) — never recomputed. On a
                  // multi-database unit the production guarantee is the per-database sum, so APH x
                  // level can sit a tenth off it; the printed quote names the databases.
                  const chainRow = (label, fn) => (
                    <tr style={{ borderTop:"1px solid #eef3ee" }}>
                      <td style={{ ...td2, textAlign:"left", color:"#5c6f5c", fontWeight:600, fontSize:11 }}>{label}</td>
                      {YRS.map((y,i) => (
                        <td key={i} colSpan={2} style={{ ...td2, background:y.bg, color:y.col, textAlign:"center", fontSize:11, borderLeft:"2px solid #e4ede4" }}>{fn(y)}</td>
                      ))}
                    </tr>
                  );
                  const pgOf = y => (y.aphY != null && y.cl && y.guar != null)
                    ? <>APH {y.aphY.toFixed(1)} × {Math.round(y.cl*100)}% = <b>{(+y.guar).toFixed(1)} bu</b></> : "—";
                  const rgOf = y => (y.guar != null && y.price != null && y.rev != null)
                    ? <>{(+y.guar).toFixed(1)} bu × {$ac(y.price * (y.pe || 1))} = <b>{$ac(y.rev)}</b></> : "—";
                  const prodRow = (label, key, bold) => (
                    <tr style={{ borderTop:"1px solid #eef3ee" }}>
                      <td style={{ ...td2, textAlign:"left", color: bold?"#1f5c2e":"#5c6f5c", fontWeight:bold ? 800 : 600 }}>{label}</td>
                      {YRS.map((y,i) => { const [p,c] = y[key]; return (
                        <React.Fragment key={i}>
                          <td style={{ ...td2, background:y.bg, fontWeight:bold ? 800 : 600, color:y.col, borderLeft:"2px solid #e4ede4" }}>{$n(p)}</td>
                          <td style={{ ...td2, background:y.bg, color:"#60735f" }}>{$n(c)}</td>
                        </React.Fragment>
                      ); })}
                    </tr>
                  );
                  return (
                    <div key={uKey + "|" + ui} style={{ marginBottom: false ? 22 : 4, paddingBottom: false ? 18 : 0, borderBottom: false ? "1px solid #eef3ee" : "none" }}>
                      <div style={{ fontSize:13.5, fontWeight:700, color:"#1a2e1a", marginBottom:6 }}>{capCty(r.county)} <span style={{ fontWeight:500, color:"#677167", fontSize:11.5 }}>· {aphProvenance({ quoteOnly: r._quoteOnly, edited: !!(edCalc && edCalc.approved != null), fromAipDb: !!(selBlend || wtd), rolled: rolled != null, priorYear: r._aphYear === MPCI_WHEAT_YEAR - 1 }).label} {aph.toFixed(1)}{aphYrs.length > 0 && (
                          <span style={{ marginLeft:5, fontSize:10.5, fontWeight:700,
                                         color: aphStale ? "#8a6100" : "#677167",
                                         background: aphStale ? "#fdf7e8" : "transparent",
                                         border: "1px solid " + (aphStale ? "#e8d5a8" : "transparent"),
                                         borderRadius:5, padding:"1px 6px" }}
                            title={aphStale
                              ? `This APH is built on ${aphYrs[0]}–${aphNewest}. ${MPCI_WHEAT_YEAR - 1} production is not keyed yet — when it lands it goes on the database and ${aphYrs[0]} drops off, which moves the guarantee.`
                              : `APH database: ${aphYrs[0]}–${aphNewest}, ${aphYrs.length} crop years.`}>
                            {aphYrs[0]}–{aphNewest}{aphStale ? ` · ${MPCI_WHEAT_YEAR - 1} not keyed` : ""}
                          </span>
                        )}{rolled != null && (
                          <span style={{ marginLeft:5, fontSize:10.5, fontWeight:800, color:"#1f5c2e",
                                         background:"#eaf3ea", border:"1px solid #b7d3b7", borderRadius:5, padding:"1px 6px" }}
                                title={`Recomputed with ${MPCI_WHEAT_YEAR - 1} production now keyed: ${storedAph.toFixed(1)} to ${rolled.toFixed(1)} bu. ${MPCI_WHEAT_YEAR - 1} goes on the ten-year database and ${aphYrs[0]} drops off. This is OUR calculation on the unit's own elected options — the AIP has not re-approved this county yet, so confirm before binding.`}>
                            {aphProvenance({ rolled: true }).label} · rolled with {MPCI_WHEAT_YEAR - 1} · was {storedAph.toFixed(1)}
                          </span>
                        )}{aphIsPriorYear && rolled == null && (
                          <span style={{ marginLeft:5, fontSize:10.5, fontWeight:800, color:"#8a6100",
                                         background:"#fdf7e8", border:"1px solid #e8d5a8", borderRadius:5, padding:"1px 6px" }}
                                title={`This is the AIP's CY${aphCropYear} approved yield. They have not issued a CY${MPCI_WHEAT_YEAR} one for this county yet, and there is no ${MPCI_WHEAT_YEAR - 1} production on file to derive one from — so the quote stands on last year's number and will move when the AIP re-approves.`}>
                            CY{aphCropYear} APH
                          </span>
                        )}{edCalc && edCalc.approved != null ? <span style={{ color:"#1f7a3d", fontWeight:700 }}> (stored {storedAph.toFixed(1)}{edCalc.taBu?`, +${edCalc.taBu} TA`:""}{edCalc.nFloored?`, ${edCalc.nFloored} YA`:""}{edCalc.nExcluded?`, ${edCalc.nExcluded} YE`:""})</span> : ""}
                        {/* Say which basis the yield came from. A weighted number that silently
                            differs from the stored summary is the kind of thing an agent finds
                            out about in front of a grower. */}
                        {!edCalc && wtd ? <span title={`${wtd.n} APH databases, acre-weighted on ${wtd.basis === "prev" ? `${aphCropYear - 1} acres (this year's are not reported yet)` : "their own acres"}. Stored summary averages them unweighted at ${storedAph.toFixed(1)}.`}
                          style={{ marginLeft:6, fontSize:10.5, background:"#eef6ec", border:"1px solid #cfe3cb", borderRadius:9, padding:"1px 7px", color:"#2d5a10", fontWeight:700, cursor:"help" }}>
                          {wtd.n} db · acre-wtd{wtd.basis === "prev" ? ` on ${aphCropYear - 1} ac` : ""}{Math.abs(wtd.aph - storedAph) > 0.05 ? ` · unwtd ${storedAph.toFixed(1)}` : ""}
                        </span> : null} · {premiumBasis ? <>{acres.toFixed(0)} ac{acreageAssumed ? <span style={{ color:"#8a6100", fontWeight:700 }}> assumed</span> : (plannedAcres && !knownAcres ? " planned" : ((isElected||r._quoteOnly) ? " est" : ""))}</> : <span style={{ color:"#8a6100", fontWeight:700 }}
                              title={priorAcres > 0
                                ? "RMA has reported no acreage for this crop year. Last year's " + priorAcres.toFixed(0) + " ac is reference only and does not price this year's premium."
                                : "RMA has reported no acreage for this crop year and there is no prior-year figure either."}>
                              acres not reported{priorAcres > 0 ? " · prior year " + priorAcres.toFixed(0) + " ac (reference)" : ""}</span>} · {r._quoteOnly ? "quote at" : isElected ? "elected · quote at" : "current"} {Math.round(curCl*100)}% {unitAssumed ? (
                            /* The agent has not stated a basis, so the quote is priced at the agency
                               default (Enterprise). Kevin, 2026-09-28: "we generally always quote as EU.
                               We dont know if they qualify for EU until they plant." So this is a stated
                               convention rather than a guess — but the figures still rest on it, and
                               where the POLICY says something else the badge names that too. The
                               guarantee is unaffected either way: unit structure moves only discFactor. */
                            <span style={{ fontWeight:800, color:"#8a6100", background:"#fdf7e8", border:"1px solid #e8d5a8", borderRadius:5, padding:"1px 6px", cursor:"help" }}
                                  title="Enterprise is the agency's standard quote basis — eligibility is not determined until acreage is reported, so no year states it at quote time. Premiums here are rated at ENTERPRISE-unit rates, the lowest RMA sells; a basic or optional unit costs more. The guarantee is unaffected. Set the structure below where you know it will be different.">
                              {unitLbl[curUnit] || curUnit} · quote basis{cardUnitSrc && cardUnitSrc.unit !== curUnit ? " · " + (cardUnitSrc.year || "prior yr") + " was " + (unitLbl[cardUnitSrc.unit] || cardUnitSrc.unit) : ""}
                            </span>
                          ) : unitLbl[curUnit]}{unitStated ? <span style={{ marginLeft:5, fontSize:10.5, fontWeight:800, color:"#1f5c2e", background:"#eef6ec", border:"1px solid #cfe3cb", borderRadius:5, padding:"1px 6px" }}
                                 title="You stated this structure; the AIP has not keyed one. The quote and the printed sheet price it and label it as entered.">entered</span> : null}</span>
                        {/* ALWAYS OFFERED. It used to appear only where the file stated no structure,
                            on the reasoning that an override would be a different feature. Kevin,
                            2026-09-28, settled that it is not: a quote is always priced at an elected
                            basis because enterprise eligibility is not known until planting. Gated on
                            the file, the control was hidden on exactly the units that were silently
                            being quoted at last year's structure. */}
                        {(
                          <span style={{ marginLeft:8, fontSize:11, color:"#5c6f5c", display:"inline-flex", alignItems:"center", gap:5 }}>
                            <span style={{ fontWeight:700 }}>Structure</span>
                            <select value={unitEntry[wheatPlannedKey(r)] || WHEAT_QUOTE_UNIT_DEFAULT}
                                    onChange={e => { const v = e.target.value; setUnitEntry(m => { const n = { ...m }; if (v) n[wheatPlannedKey(r)] = v; else delete n[wheatPlannedKey(r)]; return n; }); }}
                                    title="The unit structure this quote is priced at. Enterprise is the agency default; set Basic or Optional where you know the farm will not qualify. It prices this card and the printed quote."
                                    style={{ fontSize:11, padding:"2px 5px", borderRadius:6, border:"1px solid " + (unitEntry[wheatPlannedKey(r)] ? "#2d6a2d" : "#e8d5a8"), background:"#fff", color:"#2a3a2a" }}>
                              <option value="EU">Enterprise (default)</option>
                              <option value="BU">Basic</option>
                              <option value="OU">Optional</option>
                            </select>
                          </span>
                        )}
                        {/* ── BEGINNING FARMER / RANCHER ────────────────────────────────
                            ⚠ DERIVED, AND THE BADGE SAYS SO. M13 states no BFR field; this is
                            the AIP's own stamped subsidy read against the flat 80% SCO/ECO
                            carry — see src/bfrStatus.js. It is per POLICY, so the badge is
                            keyed on THIS unit's policy and not on the farm.

                            ⚠ AND WHAT WAS SEEN IS NOT ALWAYS WHAT IS QUOTED. The +5 first-
                            two-years tier expires, so carrying it into next year's quote
                            would under-price a document the grower signs. The badge shows
                            the observation; the line under it says what is actually being
                            applied, and they differ on purpose. */}
                        {(() => {
                          const seen = rPrev && rPrev.policy_id != null ? bfrByPolicy.get(rPrev.policy_id) : null;
                          const isSeen = seen && (seen.tier === BFR.TIER.BFR_10 || seen.tier === BFR.TIER.BFR_15);
                          // ⚠ THIS LABEL KNEW ONLY TWO TIERS AND THE ENGINE RATES FOUR.
                          // It read `bfrFirstTwoYears ? "+15" : "+10"`, so a 3rd- or
                          // 4th-year grower being quoted at +13 or +11 had "+10" printed
                          // beside the premium — the screen contradicting the number
                          // under it. It reads the SAME schedule the engine rates from.
                          const applyPts = bfrQ.bfr
                            ? Math.round(100 * (BFR.BONUS_BY_YEAR[bfrQ.bfrBenefitYear]
                                ?? (bfrQ.bfrFirstTwoYears ? BFR.BONUS_BY_YEAR[1] : BFR.BONUS_BY_YEAR[5])))
                            : null;
                          const applying = bfrQ.bfr
                            ? "+" + applyPts + (bfrQ.bfrBenefitYear
                                ? " · " + (bfrQ.bfrBenefitYear >= 5 ? "5th–10th" : ["", "1st", "2nd", "3rd", "4th"][bfrQ.bfrBenefitYear]) + " BFR crop year"
                                : "")
                            : "not applied";
                          // ⚠ THE PICKER IS KEYED ON THE YEAR, BECAUSE THE SCHEDULE IS.
                          // Kevin, 2026-10-13: "need to be able to select how many years
                          // of farming or BFR discount percentage." RMA's post-OBBBA
                          // ladder is +15 for the 1st–2nd BFR crop years, +13 for the
                          // 3rd, +11 for the 4th, +10 for the 5th–10th — so the
                          // percentage is a FUNCTION of the year and each button shows
                          // both. A free-text percentage could express +12, which RMA
                          // does not sell.
                          // ⚠ AND THIS CONTROL COULD NOT REACH TWO OF THE FOUR STEPS:
                          // it offered +10 and +15 only, so a 3rd- or 4th-year grower
                          // (+13, +11) could not be quoted correctly by hand at all.
                          const modes = [["auto", "Auto"], ["none", "Off"]]
                            .concat(BFR.BFR_YEAR_CHOICES.map((c) => [c.mode, c.label + " " + c.pct]));
                          return (
                            <div style={{ marginTop:6, display:"flex", flexWrap:"wrap", gap:8, alignItems:"center", fontSize:11 }}>
                              <span style={{ fontWeight:700, color:"#5c6f5c" }}>Beginning farmer</span>
                              {isSeen ? (
                                <span title={seen.why}
                                  style={{ background:"#eef6ec", border:"1px solid #cfe3cb", borderRadius:9, padding:"1px 8px", fontWeight:800, color:"#1f7a3d" }}>
                                  {/* ⚠ DERIVED AND STATED ARE DIFFERENT CLAIMS. Since sql/060 the AIP's own
                                      P14 #32 waiver code can SAY beginning farmer; everything else here is
                                      inferred from the subsidy a rate implies. A badge that reads derived
                                      over a stated fact undersells it, and one that reads AIP file over an
                                      inference is the invented-source failure this repo opens with. */}
                                  {seen.tier === BFR.TIER.BFR_15 ? "+15" : "+10"} on {MPCI_WHEAT_YEAR - 1}
                                  {String(seen.method || "").startsWith("STATED") ? " · AIP file" : " · derived"}
                                </span>
                              ) : (
                                <span title={seen ? seen.why : "no SCO/ECO on this policy and no comparable peer group for its MPCI rate"}
                                  style={{ background:"#f4f6f4", border:"1px solid #e0e6e0", borderRadius:9, padding:"1px 8px", color:"#7b8a7b" }}>
                                  {seen && seen.tier === BFR.TIER.NONE ? "not indicated" : "cannot tell"}
                                </span>
                              )}
                              {/* ⚠ "NOT INDICATED" ON ITS OWN READS AS "WE DO NOT KNOW", AND IT IS
                                  USUALLY A FACT. BFR is per POLICY, so a farmer name can hold one
                                  policy that qualifies and one that does not — HOWLETT FARMS is the
                                  worked case: wheat MP-0927356 reads 0.800 (standard) while corn
                                  MP-0936636 reads 0.950 AND carries the AIP's own waiver code,
                                  because the next generation took the corn over. Kevin, 2026-10-13:
                                  "I selected BFR for Howlett Farms (which I should not manually have
                                  to do, we should know that howlett is BFR from M13 data)" — we do
                                  know, and for the policy being quoted the answer is no. Saying only
                                  "not indicated" left him to override blind on the one surface where
                                  the sibling policy settles it. This states the disagreement; it does
                                  NOT apply it, because the other policy is a different insured. */}
                              {!isSeen && bfrSibling ? (
                                <span title={bfrSibling.why || ""}
                                  style={{ background:"#fdf6e8", border:"1px solid #ecdca6", borderRadius:9, padding:"1px 8px", color:"#8a6100" }}>
                                  but {bfrSibling.policyNumber} ({bfrSibling.crops}) reads{" "}
                                  {bfrSibling.tier === BFR.TIER.BFR_15 ? "+15" : "+10"} — BFR is per policy, not per farm
                                </span>
                              ) : null}
                              <span style={{ color:"#7b8a7b" }}>quoting <b style={{ color: bfrQ.bfr ? "#1f7a3d" : "#5c6f5c" }}>{applying}</b></span>
                              {bfrMode === "auto" && isSeen && seen.tier === BFR.TIER.BFR_15 && !bfrQ.bfrFirstTwoYears ? (
                                <span style={{ color:"#8a6100" }}>
                                  the extra 5 points are the FIRST TWO crop years only — confirm before applying
                                </span>
                              ) : null}
                              <span style={{ display:"inline-flex", gap:4 }}>
                                {modes.map(([m, lbl]) => (
                                  <button key={m} onClick={() => setBfrMode(m)}
                                    style={{ fontSize:10.5, fontWeight:700, padding:"2px 8px", borderRadius:999, cursor:"pointer",
                                             border:"1px solid " + (bfrMode === m ? "#2d6a2d" : "#d5e2d5"),
                                             background: bfrMode === m ? "#2d6a2d" : "#fff", color: bfrMode === m ? "#fff" : "#5c6f5c" }}>{lbl}</button>
                                ))}
                              </span>
                            </div>
                          );
                        })()}
                        {/* WHAT THE POLICY ACTUALLY CARRIES. The toggles below are what-ifs;
                            these chips are the AIP’s own insurance_options string for this
                            county, so you can see at a glance what he already has before
                            adding anything. QL and MC show here too — they change the APH or
                            the unit, not the premium, so they never appear in the toggles. */}
                        {(() => {
                          const on = parseOptionCodes(r.insurance_options);
                          const NAME = INSURANCE_OPTION_NAME;
                          return (
                            <span style={{ marginLeft:8, display:"inline-flex", gap:4, flexWrap:"wrap", alignItems:"center", verticalAlign:"middle" }}>
                              <span style={{ fontSize:10, color:"#677167", fontWeight:700 }}>ON POLICY</span>
                              {on.length ? on.map(o => (
                                <span key={o} title={NAME[o] || o} style={{ fontSize:10.5, fontWeight:800, color:"#1f5c2e", background:"#e7f3e7",
                                              border:"1px solid #b7d3b7", borderRadius:5, padding:"1px 6px" }}>{o}</span>
                              )) : <span style={{ fontSize:10.5, color:"#677167", fontStyle:"italic" }}>none</span>}
                            </span>
                          );
                        })()}
                        {/* PLANNED ACRES. Shown only where RMA reports none and the grower has no
                            planted history either — new ground, a county elected but never planted,
                            or an APH database built entirely of T-yields. It defaults to EMPTY on
                            purpose: the premium is the one figure acres move, so a default here
                            would be a premium nobody quoted. The guarantee beside it needs nothing.  */}
                        {!reportedThisYear && (
                          <span style={{ marginLeft:10, display:"inline-flex", gap:5, alignItems:"center", verticalAlign:"middle" }}>
                            <span style={{ fontSize:10, color: acreageAssumed ? "#8a6100" : "#1f5c2e", fontWeight:700 }}>
                              {acreageAssumed ? "ACRES — PENDING" : acreagePrior ? `ACRES (${acreBasis.reference.priorYearOf || "EARLIER"} REPORTED)` : "PLANNED ACRES"}
                            </span>
                            <input type="number" min="1" step="1"
                              value={unitPlanSum > 0 ? String(Math.round(unitPlanSum * 10) / 10) : (plannedAc[wheatPlannedKey(r)] != null && plannedAc[wheatPlannedKey(r)] !== "" ? plannedAc[wheatPlannedKey(r)] : (acreageAssumed ? "" : String(Math.round(acres))))}
                              placeholder="Pending"
                              disabled={unitPlanSum > 0}
                              onChange={e => setPlannedAc(m => ({ ...m, [wheatPlannedKey(r)]: e.target.value }))}
                              title={"RMA has no reported acreage for this unit, so the premium is pending until acres are entered. The guarantee and coverage above do not depend on acreage and are exact either way. Type the real figure — acres set the enterprise/basic discount band and nothing else."}
                              style={{ width:64, fontSize:11, fontWeight:700, padding:"1px 5px", borderRadius:5,
                                       border:"1px solid " + (plannedAcres ? "#b7d3b7" : "#e8d5a8"),
                                       background: plannedAcres ? "#f4faf4" : "#fdf7e8", color:"#1a2e1a" }} />
                            {unitPlanSum > 0 && <span style={{ fontSize:10, color:"#1f5c2e", fontWeight:700 }}>= the unit acres entered below</span>}
                            {priorAcres > 0 && !plannedAcres ? (
                              <button type="button"
                                onClick={() => setPlannedAc(m => ({ ...m, [wheatPlannedKey(r)]: String(Math.round(priorAcres)) }))}
                                title="Prior-year acreage is reference only and never prices a premium on its own. Clicking copies it into PLANNED, where it is a figure you chose rather than one we assumed."
                                style={{ fontSize:10, fontWeight:700, color:"#3a6a8a", background:"#eef4f8",
                                         border:"1px solid #cfe0ea", borderRadius:5, padding:"1px 6px", cursor:"pointer" }}>
                                use last year: {priorAcres.toFixed(0)} ac
                              </button>
                            ) : null}
                            {acreageAssumed ? (
                              <span style={{ fontSize:10, color:"#8a6100" }}>
                                assumed — nothing is on file for this unit; type the real figure to price the premium on it
                              </span>
                            ) : acreagePrior ? (
                              <span style={{ fontSize:10, color:"#3a6a8a" }}>
                                quoted on the {acres.toFixed(0)} ac last reported for {acreBasis.reference.priorYearOf || "an earlier year"} — {MPCI_WHEAT_YEAR} acreage is not reported until November
                              </span>
                            ) : null}
                          </span>
                        )}
                        {/* PRACTICE. Always shown, because it is the single input that changes
                            the most on this card: rate table, T-yield, TA factor, yield history
                            AND price election. Editable so a prospect can be quoted organic and
                            so a unit whose code had to be inferred from the label can be
                            corrected — the inferred case says so rather than looking certain. */}
                        <span style={{ marginLeft:10, display:"inline-flex", gap:5, alignItems:"center", verticalAlign:"middle" }}>
                          <span style={{ fontSize:10, color:"#677167", fontWeight:700 }}>PRACTICE</span>
                          <select value={uPrac} onChange={e => setPracOv(m => ({ ...m, [oKey]: e.target.value }))}
                            style={{ fontSize:11, fontWeight:700, padding:"1px 5px", borderRadius:5,
                                     border:"1px solid " + (uPrac === "003" ? "#cfe0cf" : "#c9b27a"),
                                     background: uPrac === "003" ? "#f4faf4" : "#fdf8ec",
                                     color: uPrac === "003" ? "#1f5c2e" : "#7a5a10" }}>
                            {[...new Set([uPrac, ...wheatPractices2027(fips, county)])].map(pc =>
                              <option key={pc} value={pc}>{WHEAT_PRACTICE_NAMES[pc] || pc}</option>)}
                          </select>
                          {isCertifiedOrganic(uPrac) && (
                            <span style={{ fontSize:10.5, fontWeight:700, color: uPx.price ? "#7a5a10" : "#a5461b" }} title={uPx.note || "RMA certified-organic price election"}>
                              {uPx.price ? `$${uPx.price.toFixed(2)}/bu` : "election not announced"}{uPx.status === "organic-manual" ? " (entered)" : ""}
                            </span>
                          )}
                          {practiceInferred(r) && uPrac !== "003" && !pracOv[oKey] && (
                            <span title="No practice code on this coverage — read off the practice label, which cannot tell certified organic from transitional. Confirm before quoting."
                                  style={{ fontSize:10, fontWeight:800, color:"#8a6d1f", background:"#fbf7ee", border:"1px dashed #e3d3ab", borderRadius:5, padding:"1px 5px" }}>inferred</span>
                          )}
                        </span>
                      </div>
                      {/* NO REPORTED ACREAGE. Says which half of the card is exact and which is
                          withheld, because the failure this replaces was a card of dashes that
                          looked like a broken page rather than a missing input. */}
                      {inSet && acreageAssumed && (
                        <div style={{ fontSize:12, color:"#6b5320", background:"#fdf7e8", border:"1px solid #e8d5a8", borderRadius:8, padding:"8px 11px", marginBottom:8, lineHeight:1.5 }}>
                          <b>No acreage reported for {MPCI_WHEAT_YEAR} in {capCty(county)} — Acres Pending: the premium is not quoted until acres are entered above.</b>
                          {priorAcres > 0 ? <>{" "}Prior-year acreage ({priorAcres.toFixed(0)} ac) is shown for <b>reference only</b>; last year's ground does not price this year.</> : null}
                          {" "}The <b>guarantee and coverage are exact either way</b> — they do not depend on acreage at all.
                          {" "}Acreage moves the premium in one place, the enterprise/basic discount band, so type the real figure above before quoting a grower a cost.
                        </div>
                      )}
                      {!inSet && (
                        <div style={{ fontSize:12, color:"#a5461b", background:"#fdf3ef", border:"1px solid #e8cdc2", borderRadius:8, padding:"8px 11px", marginBottom:8 }}>
                          {!wheatCountyInSet(fips, county) ? `${capCty(county)} isn't in the ${MPCI_WHEAT_YEAR} ${WHEAT_STATES[fips] || ""} wheat rate set.`
                            : !wheatCountyInSet(fips, county, uPrac) ? `RMA doesn't offer ${WHEAT_PRACTICE_NAMES[uPrac] || uPrac} wheat in ${capCty(county)} County.`
                            : uPx.note || `No ${MPCI_WHEAT_YEAR} price election announced for this practice — nothing to quote against.`}
                        </div>
                      )}
                      {inSet && (() => {
                        // ── THE COVERAGE PANEL ────────────────────────────────────────
                        // ONE place to set a coverage. Unit structure, the level being
                        // quoted, SCO/ECO, the yield options and the practice all live here
                        // and all drive the same three things: the recommendation column,
                        // the comparison bars and the reference grid.
                        //
                        // Before this there were four: an options bar above the county tabs,
                        // unit/level/SCO/ECO inside the recommendation column, a second
                        // unit/level/SCO/ECO row on the grid, and an "add a level" row over
                        // the bars. Only some of them moved the bars, so the same setting
                        // read differently depending on which one you touched (Kevin,
                        // 2026-08-06: "only one edits the bars").
                        const pill = on => ({ fontSize:11.5, fontWeight:700, padding:"3px 10px", borderRadius:6, cursor:"pointer",
                          border:"1px solid " + (on ? "#2d6a2d" : "#d5e2d5"), background: on ? "#2d6a2d" : "#fff", color: on ? "#fff" : "#5c6f5c" });
                        const chip = on => ({ ...pill(on), padding:"3px 8px", fontSize:11 });
                        const lbl = { fontSize:10.5, color:"#60735f", fontWeight:800, textTransform:"uppercase", letterSpacing:".05em", minWidth:64 };
                        const optBtn = on => ({ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:5, cursor:"pointer",
                          border:"1px solid " + (on ? "#2d6a2d" : "#d5e2d5"), background: on ? "#eaf3ea" : "#fff", color: on ? "#1f7a3d" : "#8a978a" });
                        return (
                          <div style={{ border:"1px solid #d5e2d5", borderRadius:10, background:"#f7faf7", padding:"9px 12px", marginBottom:10 }}>
                            {/* WHOSE PICK IS THIS. The panel edits what gets quoted AND what gets
                                printed, so the one thing it must never be is ambiguous about
                                whether the number on screen is the engine's answer or a person's.
                                Silas's own answer is stated in full either way — an override that
                                hides what it overrode cannot be reconsidered. */}
                            {rec && (
                              <div style={{ display:"flex", gap:9, alignItems:"center", flexWrap:"wrap", marginBottom:8,
                                            padding:"5px 9px", borderRadius:7,
                                            border:"1px solid " + (recOverridden ? "#e3bf63" : "#cfe3cf"),
                                            background: recOverridden ? "#fdf6e3" : "#eef6ee" }}>
                                <span style={{ fontSize:10, fontWeight:800, letterSpacing:".05em",
                                               color: recOverridden ? "#8a6414" : "#1f5c2e" }}>
                                  {recOverridden ? "\u26A0 OVERRIDDEN" : "\u2713 SILAS'S RECOMMENDATION"}
                                </span>
                                <span style={{ fontSize:11.5, color:"#4a5a4a" }} title={rec.keepReason || rec.why || ""}>
                                  Silas recommends <b>{Math.round(rec.cl*100)}% {unitLbl[rec.unit] || rec.unit}{rec.sco ? " + SCO" : ""}{rec.eco ? " + ECO" : ""}</b>
                                  {/* ⚠ "and that is what prints" WAS TRUE OF THIS UNIT AND READ AS THE WHOLE
                                      DOCUMENT. A farm with two units prints a page each, and the other page
                                      carries ITS own coverage — which is how SCO survived an untick. Say which
                                      unit this is, whenever there is more than one. */}
                                  {recOverridden ? <> &mdash; you are quoting <b>{Math.round(rc.cl*100)}% {unitLbl[rc.unit] || rc.unit}{rc.sco ? " + " + areaCoverageLabel("SCO", rc.scoPct) : ""}{rc.eco ? " + " + areaCoverageLabel("ECO", rc.ecoPct) : ""}</b>, and that is what prints{units.length > 1 ? <> for <b>{unitLabelOf(r)}</b></> : null}.</> : null}
                                </span>
                                {recOverridden && (
                                  <button onClick={clearRc}
                                    title="Discard the override and quote Silas's recommendation"
                                    style={{ ...pill(false), borderColor:"#8a6414", color:"#8a6414" }}>Use Silas's recommendation</button>
                                )}
                                {/* The other units are not re-quoted here — only the open tab renders — so
                                    this states the SCOPE rather than guessing what they carry, and offers the
                                    one action that makes the whole document agree. */}
                                {recOverridden && units.length > 1 && (
                                  <>
                                    <span style={{ fontSize:11, color:"#8a6414", flexBasis:"100%" }}>
                                      This is set for <b>{unitLabelOf(r)}</b> only. {otherUnits.length === 1
                                        ? <>The other unit ({otherUnits.map(unitLabelOf).join(", ")}) still prints its own coverage.</>
                                        : <>The other {otherUnits.length} units ({otherUnits.map(unitLabelOf).join(", ")}) still print their own coverage.</>}
                                    </span>
                                    <button onClick={applyCoverageToAllUnits}
                                      title={`Quote every unit on this farm at ${Math.round(rc.cl*100)}% ${unitLbl[rc.unit] || rc.unit}${rc.sco ? " + " + areaCoverageLabel("SCO", rc.scoPct) : ""}${rc.eco ? " + " + areaCoverageLabel("ECO", rc.ecoPct) : ""} — it overwrites whatever the other units are set to`}
                                      style={{ ...pill(false), borderColor:"#8a6414", color:"#8a6414" }}>Apply to all {units.length} units</button>
                                  </>
                                )}
                              </div>
                            )}
                            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:6 }}>
                              <span style={lbl}>Coverage</span>
                              {[["OU","Optional"],["BU","Basic"],["EU","Enterprise"]].map(([v,t]) =>
                                <button key={v} onClick={() => setRc({ unit:v })} style={pill(rc.unit===v)}>{t}</button>)}
                              <span style={{ width:1, height:18, background:"#dde8dd", margin:"0 2px" }} />
                              {/* ⚠ THIS ROW SETS THE QUOTE. THE "Stacks" ROW BELOW ONLY ADDS A CHART BAR,
                                  and until now neither group of level chips carried a label of its own —
                                  two rows of the same eight numbers, one of which changes the recommended
                                  coverage and one of which changes which bars are drawn. Naming this one
                                  "Level" is what tells them apart at a glance. */}
                              {/* ⚠ ONE ROW OF LEVELS, NOT TWO (2026-09-23). There were two rows of the
                                  same eight numbers — this one set the QUOTE, and a second further
                                  down labelled "Stacks" only added a bar to the chart. Kevin quoted
                                  HARTWAY FARMS at 80% and the report printed 50%, which is exactly
                                  what clicking the wrong row does, and then: "simplify the quoter,
                                  it is getting very busy and confusing… definitely get rid of the 2
                                  rows."
                                  The comment 70 lines above already records this panel going from
                                  FOUR rows to fewer because "only one edits the bars" (2026-08-06).
                                  It grew back. One row now, and the two jobs are one click apart
                                  rather than one row apart:
                                    click            -> quote this unit at that level
                                    alt/ctrl-click   -> add or remove it as a comparison stack
                                  Both are named in every chip's tooltip, and the chip's own styling
                                  says which state it is in, so neither is hidden behind a gesture
                                  nobody is told about. */}
                              <span style={{ ...lbl, minWidth:0 }} title="The coverage level this unit is QUOTED at. Click to quote; alt-click (or ctrl-click) to add a level as a comparison stack.">Level</span>
                              {LEVELS.map(l => {
                                const isQuoted = Math.abs(rc.cl - l) < 1e-6;
                                const onChart = chartLv.some(x => Math.abs(x - l) < 1e-9);
                                const full = chartLv.length >= WHEAT_BAR_MAX && !onChart;
                                const pc = Math.round(l * 100);
                                return (
                                  <button key={l}
                                    onClick={(e) => {
                                      // The modifier is the COMPARE gesture. The quoted level is always
                                      // drawn, so alt-clicking it would be a no-op the chart could not
                                      // honour — that is refused rather than silently ignored.
                                      if (e.altKey || e.ctrlKey || e.metaKey) {
                                        if (!isQuoted && !full) toggleChartLevel(l);
                                      } else setRc({ cl: l });
                                    }}
                                    title={isQuoted
                                      ? pc + "% is what this unit is quoted at — the recommendation and every printed document use it."
                                      : "Click to quote this unit at " + pc + "%."
                                        + (onChart ? "  Alt-click to take its comparison stack off the chart and the printed quote."
                                                   : full ? "  Already showing " + WHEAT_BAR_MAX + " stacks — remove one to compare this level."
                                                          : "  Alt-click to compare it alongside without changing the quote.")}
                                    style={{ ...(isQuoted ? chip(true) : onChart ? optBtn(true) : chip(false)),
                                             ...(onChart && !isQuoted ? { borderStyle:"dashed" } : null) }}>
                                    {pc}
                                  </button>
                                );
                              })}
                              <span style={{ fontSize:10, color:"#677167" }}
                                    title="Coverage stacks drawn on the chart and on the printed quote. The quoted level is always one of them.">
                                {chartLv.length}/{WHEAT_BAR_MAX} shown
                              </span>
                              {barsCustom && (
                                <button onClick={() => resetBarLevels(uKey)}
                                  title="Back to the levels Silas picks for this unit — the quote, its neighbours and last year"
                                  style={{ ...optBtn(false), color:"#8a6414", borderColor:"#e3bf63" }}>auto</button>
                              )}
                              <span style={{ width:1, height:18, background:"#dde8dd", margin:"0 2px" }} />
                              <label style={{ fontSize:12, color:"#3a4a6a", cursor:"pointer" }}><input type="checkbox" checked={!!rc.sco} onChange={e => setRc({ sco:e.target.checked })} /> SCO</label>
                              <AreaCovPctSlider name="SCO" pct={rc.scoPct} on={!!rc.sco} onChange={v => setRc({ scoPct: v })} compact />
                              <label style={{ fontSize:12, color:"#3a4a6a", cursor:"pointer" }}><input type="checkbox" checked={!!rc.eco} onChange={e => setRc({ eco:e.target.checked })} /> ECO</label>
                              <AreaCovPctSlider name="ECO" pct={rc.ecoPct} on={!!rc.eco} onChange={v => setRc({ ecoPct: v })} compact />
                            </div>
                            {((rc.sco && +rc.scoPct < 100) || (rc.eco && +rc.ecoPct < 100)) && (
                              <div style={{ fontSize:10.5, color:"#60735f", margin:"-2px 0 6px" }}>
                                SCO and ECO are quoted on the share of each band set here — coverage and premium both scale with it. Silas still chooses the level with each band at 100%.
                              </div>
                            )}
                            <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                              <span style={lbl}>Options</span>
                              {/* OPTIONS ARE NOT OFFERED EVERYWHERE, and the two the panel pushes
                                  hardest are the two least available. Straight off the ADM
                                  (A01060) for 2027 winter wheat: YA/YC/QL/MC/LP/HF/EI/EO are on
                                  100% of county x practice combos in all five states, but TA is
                                  on 400 of 1,104 (36%) and YE on 606 (55%) — and it splits
                                  WITHIN one farm. Meisenzahl gets YE in Ontario and not in
                                  Monroe; Wyoming NY sells no TA at all. Offering them
                                  everywhere invites an election RMA will not accept, so
                                  availability is read, never assumed — same rule as PF. */}
                              {(() => {
                                const avail = wheatOptions2027(fips, county, uPrac);
                                const OPT = [["ta","TA","Trend Adjustment"],["ya","YA","Yield Adjustment"],
                                             ["yc","YC","Yield Cup"],["ye","YE","Yield Exclusion"],
                                             ["mc","MCEU","Multi-County Enterprise Unit — qualifies a county that could not form an EU on its own. Recorded; it does not change the premium."],
                                             ["pf","PF","Prevented-planting coverage buy-up"]];
                                return OPT.map(([k, t, name]) => {
                                  const code = k === "mc" ? "MC" : t;
                                  const offered = k === "pf" ? ppF.pfOffered : avail.includes(code);
                                  if (!offered) return (
                                    <button key={k} disabled
                                      title={name + " — RMA does not offer " + code + " on " + MPCI_WHEAT_YEAR + " wheat in " + capCty(county) + (uPrac === "003" ? "" : " (" + (WHEAT_PRACTICE_NAMES[uPrac] || uPrac) + ")") + ". Not an app limit: there is no " + code + " row in the actuarial data."}
                                      style={{ ...optBtn(false), cursor:"not-allowed", color:"#c3cec3", borderStyle:"dashed" }}>{t}</button>
                                  );
                                  const extra = k === "pf" && ppF.pfFactor != null
                                    ? " — raises PP from " + Math.round(ppF.base*100) + "% to " + Math.round(ppF.pfFactor*100) + "% of the guarantee" : "";
                                  return (
                                    <button key={k} title={name + extra}
                                      onClick={() => setFarmOpts(m => ({ ...m, [oKey]: { ...(m[oKey] || FARM_OPTS_OFF), [k]: !fo[k] } }))}
                                      style={optBtn(fo[k])}>{t}</button>
                                  );
                                });
                              })()}
                              {/* The "Stacks" row of the same eight numbers lived here and is GONE —
                                  merged into the Level row above, where alt-click does its job.
                                  `toggleChartLevel`, `chartLv`, `barsCustom` and `resetBarLevels`
                                  are unchanged and still the only rule that decides which stacks
                                  draw; only the control moved. */}
                            </div>
                          </div>
                        );
                      })()}
                      {inSet && (() => {
                        // Editable APH database for this unit: seed from production_history, add this year's (2026) production.
                        const yr1 = MPCI_WHEAT_YEAR - 1;
                        const seed = () => { const s = yieldSeries("WHEAT", county, uPrac).map(o => ({ year:o.yr, y:String(Math.round(o.y*10)/10) })).sort((a,b)=>b.year-a.year).slice(0,10); if (!s.some(x => +x.year === yr1)) s.unshift({ year:yr1, y:"" }); return s.length ? s : [{ year:yr1, y:"" },{ year:yr1-1, y:"" },{ year:yr1-2, y:"" },{ year:yr1-3, y:"" }]; };
                        const cur = aphEdit[uKey];
                        const open = cur && cur.open;
                        const rows = cur ? cur.rows : null;
                        const set = (patch) => setAphEdit(m => ({ ...m, [uKey]: { ...(m[uKey]||{}), ...patch } }));
                        const setRow = (i, y) => set({ rows: rows.map((x,ix) => ix===i ? { ...x, y } : x), applied:false });
                        const setYr = (i, year) => set({ rows: rows.map((x,ix) => ix===i ? { ...x, year } : x), applied:false });
                        // Same 10-year ceiling as the quoter's builder — see APH_MAX_YEARS.
                        // `rows` is NULL until the editor is opened for this county, and this
                        // runs at render where addRow/delRow only run on click — so it must
                        // guard. It did not, and crashed every farm card that reached here.
                        const atMax = !!rows && rows.length >= APH_MAX_YEARS;
                        const addRow = () => { if (rows.length >= APH_MAX_YEARS) return; const mn = Math.min(...rows.map(x => +x.year || yr1)); set({ rows:[...rows, { year:mn-1, y:"" }], applied:false }); };
                        const delRow = (i) => set({ rows: rows.length>1 ? rows.filter((_,ix)=>ix!==i) : rows, applied:false });
                        const iC = { width:58, fontSize:12, padding:"3px 5px", borderRadius:5, border:"1px solid #d5e2d5", textAlign:"right" };
                        // ── THE APH DATABASES, ONE PER UNIT ──────────────────────────────
                        // NAU quotes each unit and offers the average as a checkbox, and that
                        // is the correct default in both directions: RMA approves and rates
                        // each database separately and the liabilities SUM, so quoting one
                        // blended yield is not just a different view, it is a different (and
                        // lower) number — the rate is convex in approved/adjusted, and on
                        // Kennedy's eleven the shortcut came out 4.7% light.
                        const dbKey = county + "|" + uPrac;
                        // ONE SOURCE with the quote — unitDbsFor() unions this year's databases
                        // with last year's, so the panel can never list a set the page is not
                        // pricing, and a database missing from this year's file (Kennedy's 0007
                        // and 0008, the only two he plants) still appears with its acres.
                        // ONE ROW PER CROP YEAR. A database is carried on every policy year it
                        // belongs to, so the same 2025 record is stored once under the 2026
                        // policy and again under the 2027 one — real rows, not a sync bug, but
                        // the database itself has ONE record for that year and the panel must
                        // show it once. Collapse on the same severity order the sync dedupes
                        // with, so an actual beats a Z if the two years ever disagree.
                        const SEV_Y = { A:4, PA:4, TA:3, T:2, I:2, Z:1, PP:6, PW:6 };
                        const planMap = unitAc[county + "|" + uPrac] || {};
                        const setUnitAcre = (k, v) => setUnitAc(m => ({ ...m, [county + "|" + uPrac]: { ...(m[county + "|" + uPrac] || {}), [k]: v } }));
                        const dbs = unitDbsFor(county, uPrac, aphCropYear, planMap).map(d => {
                          const byYr = {};
                          for (const a of (aphRows || [])) {
                            if ((a.crop||"") !== "WHEAT" || (a.county||"").toUpperCase() !== county) continue;
                            if (`${(a.unit_number||"").trim()}-${(a.sub_unit||"").trim()}` !== d.k) continue;
                            const cur = byYr[a.crop_year];
                            if (!cur || (SEV_Y[String(a.record_type||"").toUpperCase()]||0) > (SEV_Y[String(cur.record_type||"").toUpperCase()]||0)) byYr[a.crop_year] = a;
                          }
                          return { ...d, yrs: Object.values(byYr).sort((p2,q2) => q2.crop_year - p2.crop_year) };
                        });
                        const dbAc = dbs.reduce((s,d) => s + d.ac, 0);
                        // QUOTE EACH DATABASE ON ITS OWN THREE YIELDS. This is the actual
                        // calculation, not a presentation of one: RMA rates each database
                        // separately and sums the liabilities, and the rate is convex in
                        // approved/adjusted, so a single blended quote is a different (lower)
                        // number — 4.7% light on Kennedy's eleven. Acres drive only the
                        // ENTERPRISE DISCOUNT BAND, which is keyed to the whole unit's acreage,
                        // so every database is banded on the total and then carries its own
                        // per-acre result. Verified against NAU's own quote at 240.52 / 389.78.
                        const bandAc = dbAc > 0 ? dbAc : (+acres || 0);
                        const qOne = (d) => {
                          const a = +d.u.aph_yield; if (!(a > 0) || !bandAc || !(+price > 0)) return null;
                          const m = quoteWheatRP2027({ ...bfrQ, state:fips, county, practice:uPrac, aph:a,
                            rateYield: +d.u.rate_yield > 0 ? +d.u.rate_yield : null,
                            adjustedYield: +d.u.adjusted_yield > 0 ? +d.u.adjusted_yield : (+d.u.rate_yield > 0 ? +d.u.rate_yield : null),
                            yieldOptions: typeof d.u.yield_options === "string" ? d.u.yield_options : null,
                            electedOptions: r.insurance_options ?? null,
                            cl: rc.cl, acres: bandAc, unit: rc.unit, price, vol });
                          if (!m) return null;
                          const s = rc.sco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, practice:uPrac, endorsement:"SCO", baseCl:rc.cl, aph:a, acres:bandAc, price, vol, coveragePct: areaCoverageFractionOfPct(rc.scoPct) }) : null;
                          const e = rc.eco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, practice:uPrac, endorsement:"ECO", baseCl:rc.cl, aph:a, acres:bandAc, price, vol, coveragePct: areaCoverageFractionOfPct(rc.ecoPct) }) : null;
                          return { guar:m.guarBu, cov:(m.liabPerAc||0)+(s?s.covPerAc:0)+(e?e.covPerAc:0),
                                   // No acres on any database AND none for the unit: the premium would be banded on
                                   // an invented acreage, so it is withheld; guarantee and coverage do not depend on it.
                                   prem:(dbAc > 0 || premiumBasis) ? (m.netAcre||0)+(s?s.netAcre:0)+(e?e.netAcre:0) : null };
                        };
                        // Sum the ones carrying acres — that IS the policy total, the same way
                        // his 2026 units sum to the $97,382 the AIP billed.
                        // The total follows the SELECTION — quoting three units should total those
                        // three, not the farm they sit in. With nothing ticked it is the operation.
                        const totOver = (unitSel[dbKey] || []).length
                          ? dbs.filter(d => (unitSel[dbKey] || []).includes(d.k)) : dbs;
                        const qTot = totOver.reduce((t, d) => {
                          const q2 = d.ac > 0 ? qOne(d) : null;
                          return q2 ? { ac:t.ac+d.ac, bu:t.bu+q2.guar*d.ac, cov:t.cov+q2.cov*d.ac, prem:t.prem+q2.prem*d.ac } : t;
                        }, { ac:0, bu:0, cov:0, prem:0 });
                        const rolled = !!rollUp[dbKey];
                        const openSet = openDb[dbKey] || {};
                        const tdS = { padding:"3px 7px", textAlign:"right", fontSize:12, borderBottom:"1px solid #f0f5f0" };
                        // The printout marks its records and so do we: a T or PP row IS one of
                        // the ten, a Z row is not in the database at all.
                        const selKeys = unitSel[dbKey] || [];
                        const toggleDb = k => setUnitSel(m => {
                          const cur = m[dbKey] || [];
                          return { ...m, [dbKey]: cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k] };
                        });
                        const rtChip = rt => { const t = String(rt||"").toUpperCase();
                          if (t === "Z") return { bg:"#f2f2f2", fg:"#9a9a9a", lbl:"Z · not in database" };
                          if (t === "T") return { bg:"#fbf3dc", fg:"#8a6d1f", lbl:"T · transitional" };
                          if (t === "PP" || t === "PW") return { bg:"#fdeee6", fg:"#a5461b", lbl:t + " · prevent plant" };
                          return { bg:"#eef6ec", fg:"#2d5a10", lbl:t || "—" }; };
                        return (
                          <div style={{ marginBottom:8 }}>
                            {dbs.length > 1 && (
                              <div style={{ border:"1px solid #cfe0cf", borderRadius:9, padding:"9px 11px", background:"#fbfdfb", marginBottom:8 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:10, flexWrap:"wrap", marginBottom:7 }}>
                                  <span style={{ fontSize:10.5, textTransform:"uppercase", letterSpacing:".04em", color:"#60735f", fontWeight:700 }}>
                                    APH databases · {dbs.length} units{dbAc > 0 ? ` · ${dbAc.toFixed(1)} ac${dbs.some(d=>d.acFrom==="planned") ? ` (estimated ${aphCropYear} acres)` : dbs.some(d=>d.acFrom==="prev") ? ` (${aphCropYear-1} acres)` : ""}` : ""}
                                    {rolled
                                      ? <span style={{ color:"#8a6d1f" }}> · whole-farm average</span>
                                      : selKeys.length === 1
                                      ? <span style={{ color:"#1f5c2e" }}> · quoting {selKeys[0]}</span>
                                      : selKeys.length > 1
                                      ? <span style={{ color:"#1f5c2e" }}> · quoting {selKeys.length} units{selBlend ? ` · ${selBlend.aph} bu ${selBlend.basis === "equal" ? "avg (no acres to weight)" : "acre-wtd"}` : ""}</span>
                                      : <span style={{ color:"#677167", textTransform:"none", letterSpacing:0, fontWeight:500 }}> — whole operation. Tick units to quote just those.</span>}
                                  </span>
                                  <span style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                                    {!rolled && selKeys.length > 0 && (
                                      <button onClick={() => setUnitSel(m => ({ ...m, [dbKey]: [] }))}
                                              style={{ fontSize:10.5, fontWeight:700, padding:"2px 9px", borderRadius:5, cursor:"pointer", border:"1px solid #2d6a2d", background:"#2d6a2d", color:"#fff" }}>
                                        ← whole operation
                                      </button>
                                    )}
                                    {!rolled && selKeys.length !== dbs.length && (
                                      <button onClick={() => setUnitSel(m => ({ ...m, [dbKey]: dbs.map(d => d.k) }))}
                                              style={{ fontSize:10.5, fontWeight:700, padding:"2px 9px", borderRadius:5, cursor:"pointer", border:"1px solid #cfe0cf", background:"#fff", color:"#2d5a10" }}>
                                        select all
                                      </button>
                                    )}
                                    <label style={{ fontSize:11, color:"#3a4a3a", fontWeight:700, display:"flex", alignItems:"center", gap:5, cursor:"pointer" }}>
                                      <input type="checkbox" checked={rolled} onChange={e => { setRollUp(m => ({ ...m, [dbKey]: e.target.checked })); if (e.target.checked) setUnitSel(m => ({ ...m, [dbKey]: [] })); }} />
                                      Roll up to one average
                                    </label>
                                  </span>
                                </div>
                                {rolled ? (
                                  <div style={{ fontSize:12.5, color:"#3a4a3a" }}>
                                    {wtd ? <>Acre-weighted <b style={{ color:"#1f5c2e", fontSize:15 }}>{wtd.aph}</b> bu — rate {wtd.rate} / adjusted {wtd.adjusted} over {wtd.acres} ac.{" "}
                                      <span style={{ color:"#8a6d1f" }}>Unweighted, every database counts the same: {storedAph.toFixed(1)}.</span></>
                                    : <>No acres on file to weight by — the stored average is <b>{storedAph.toFixed(1)}</b>, which counts dormant ground the same as planted.</>}
                                  </div>
                                ) : (
                                  <div style={{ overflowX:"auto" }}>
                                  <table style={{ width:"100%", borderCollapse:"collapse", minWidth:620 }}>
                                    <thead><tr style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".04em", color:"#677167" }}>
                                      <th style={{ textAlign:"left", padding:"2px 7px", fontWeight:700 }}>Unit</th>
                                      <th style={{ ...tdS, fontWeight:700 }}>Acres</th><th style={{ ...tdS, fontWeight:700 }}>Approved</th>
                                      <th style={{ ...tdS, fontWeight:700 }}>Rate</th><th style={{ ...tdS, fontWeight:700 }}>Adjusted</th>
                                      <th style={{ ...tdS, fontWeight:700 }}>Records</th>
                                      <th style={{ ...tdS, fontWeight:700, borderLeft:"1px solid #e4eee4" }}>Guar bu</th>
                                      <th style={{ ...tdS, fontWeight:700 }}>Cov /ac</th>
                                      <th style={{ ...tdS, fontWeight:700 }}>Prem /ac</th>
                                    </tr></thead>
                                    <tbody>
                                      {dbs.map(d => {
                                        const isOpen = !!openSet[d.k];
                                        const inDb = d.yrs.filter(y => String(y.record_type||"").toUpperCase() !== "Z" && +y.yield_per_acre > 0);
                                        const own = inDb.length ? Math.round(inDb.reduce((s,y)=>s+ +y.yield_per_acre,0)/inDb.length) : null;
                                        const dq = qOne(d);
                                        const isSel = selKeys.includes(d.k);
                                        return (
                                          <React.Fragment key={d.k}>
                                            <tr onClick={() => toggleDb(d.k)}
                                                title={isSel ? "In the quote — click to drop it" : "Click to add this database to the quote"}
                                                style={{ cursor:"pointer", background: isSel ? "#e4f1e0" : (d.ac > 0 ? "#f4faf4" : "transparent"),
                                                         boxShadow: isSel ? "inset 3px 0 0 #2d6a2d" : "none" }}>
                                            <td style={{ padding:"3px 7px", fontSize:12, fontWeight:700, borderBottom:"1px solid #f0f5f0" }}>
                                              <span onClick={e => { e.stopPropagation(); if (d.yrs.length) setOpenDb(m => ({ ...m, [dbKey]: { ...(m[dbKey]||{}), [d.k]: !isOpen } })); }}
                                                    title={d.yrs.length ? "Show the year records" : "Year records populate on the next sync"}
                                                    style={{ color: d.yrs.length ? "#60735f" : "#dfe7df", marginRight:5, cursor: d.yrs.length ? "pointer" : "default", padding:"0 3px" }}>
                                                {d.yrs.length ? (isOpen ? "▾" : "▸") : "·"}
                                              </span>
                                              <input type="checkbox" checked={isSel} readOnly tabIndex={-1}
                                                     style={{ marginRight:6, verticalAlign:"middle", cursor:"pointer", accentColor:"#2d6a2d" }} />
                                              {d.k}
                                              {isSel && <span style={{ marginLeft:5, fontSize:10.5, background:"#2d6a2d", color:"#fff", borderRadius:4, padding:"1px 5px", letterSpacing:".03em" }}>QUOTING</span>}
                                              {d.acFrom === "planned"
                                                ? <span style={{ fontWeight:500, color:"#1f5c2e", fontSize:10.5 }}> · {aphCropYear} est. acres</span>
                                                : d.histAc > 0
                                                ? (d.histFrom === "prev" ? <span style={{ fontWeight:500, color:"#60735f", fontSize:10.5 }}> · {aphCropYear-1} acres</span> : null)
                                                : <span style={{ fontWeight:500, color:"#677167", fontSize:10.5 }}> · no acres reported</span>}
                                              {d.yieldsFrom !== aphCropYear && <span style={{ fontWeight:500, color:"#8a6d1f", fontSize:10.5 }}> · {d.yieldsFrom} yields</span>}
                                            </td>
                                            <td style={tdS} onClick={e => e.stopPropagation()}>
                                              {/* ESTIMATED PLANTED ACRES for the quote year — Mike, 2026-09-18. Typed figures
                                                  are the weights once ANY unit carries one; a unit left blank is not being
                                                  planted. The placeholder is last year's reported acres, shown, never assumed. */}
                                              <input type="number" min="0" step="0.1" value={planMap[d.k] ?? ""}
                                                placeholder={d.histAc > 0 ? d.histAc.toFixed(1) : "0"}
                                                onChange={ev => setUnitAcre(d.k, ev.target.value)}
                                                title={`Estimated ${aphCropYear} planted acres on ${d.k}. Leave every unit blank to weight by ${aphCropYear - 1}'s reported acres.`}
                                                style={{ width:58, fontSize:11.5, textAlign:"right", padding:"1px 4px", borderRadius:5,
                                                         border:"1px solid " + (+planMap[d.k] > 0 ? "#2d6a2d" : "#d5e2d5"),
                                                         background: +planMap[d.k] > 0 ? "#eef6ec" : "#fff" }} />
                                            </td>
                                            <td style={{ ...tdS, fontWeight:700 }}>{+d.u.aph_yield || "—"}</td>
                                            <td style={tdS}>{+d.u.rate_yield || "—"}</td>
                                            <td style={tdS}>{+d.u.adjusted_yield || +d.u.rate_yield || +d.u.aph_yield || "—"}</td>
                                            <td style={{ ...tdS, color:"#60735f" }}>{d.yrs.length ? `${inDb.length} yr${inDb.length===1?"":"s"}` : "—"}</td>
                                            <td style={{ ...tdS, borderLeft:"1px solid #e4eee4", fontWeight:700 }}>{dq ? dq.guar.toFixed(1) : "—"}</td>
                                            <td style={tdS}>{dq ? "$" + dq.cov.toFixed(2) : "—"}</td>
                                            <td style={{ ...tdS, fontWeight:700, color:"#1f5c2e" }}>{dq && dq.prem != null ? "$" + dq.prem.toFixed(2) : "—"}</td>
                                          </tr>
                                          {isOpen && d.yrs.length > 0 && (
                                            <tr><td colSpan={9} style={{ padding:"4px 7px 9px 22px", borderBottom:"1px solid #f0f5f0" }}>
                                              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                                                {d.yrs.map(y => { const c = rtChip(y.record_type); return (
                                                  <span key={y.crop_year + (y.record_type||"")} title={`${y.crop_year} · ${c.lbl}${y.acres?` · ${(+y.acres).toFixed(1)} ac`:""}${y.production?` · ${(+y.production).toLocaleString()} bu`:""}${y.production_status==="M"?" · claim for indemnity":""}`}
                                                        style={{ background:c.bg, color:c.fg, border:"1px solid rgba(0,0,0,.06)", borderRadius:6, padding:"2px 7px", fontSize:11, fontWeight:700, cursor:"help" }}>
                                                    <span style={{ opacity:.65, fontWeight:500 }}>{y.crop_year}</span>{" "}
                                                    {+y.yield_per_acre > 0 ? (+y.yield_per_acre).toFixed(0) : "0"}
                                                    <span style={{ opacity:.65, fontWeight:500 }}>{String(y.record_type||"").toUpperCase()}</span>
                                                    {y.production_status === "M" ? <span style={{ color:"#a5461b" }}> ⚑</span> : null}
                                                  </span>); })}
                                              </div>
                                              {own != null && (
                                                <div style={{ fontSize:11, color:"#60735f", marginTop:5 }}>
                                                  {inDb.length} records average <b style={{ color:"#3a4a3a" }}>{own}</b> bu
                                                  {+d.u.rate_yield > 0 && own !== Math.round(+d.u.rate_yield)
                                                    ? <span style={{ color:"#a5461b" }}> — AIP rate yield is {+d.u.rate_yield}, so the database on file differs from ours</span>
                                                    : <span> · matches the AIP rate yield</span>}
                                                </div>
                                              )}
                                            </td></tr>
                                          )}
                                          </React.Fragment>
                                        );
                                      })}
                                      {qTot.ac > 0 && (
                                        <tr style={{ background:"#f0f6ef" }}>
                                          <td style={{ padding:"5px 7px", fontSize:12, fontWeight:800, color:"#1f5c2e" }}>
                                            {selKeys.length ? `${selKeys.length} selected` : "Policy total"}<span style={{ fontWeight:500, color:"#60735f" }}> · {Math.round(rc.cl*100)}% {unitLbl[rc.unit] || rc.unit}{rc.sco?" + SCO":""}{rc.eco?" + ECO":""}</span>
                                          </td>
                                          <td style={{ ...tdS, fontWeight:800 }}>{qTot.ac.toFixed(1)}</td>
                                          <td colSpan={4} style={{ ...tdS, color:"#60735f", fontSize:11 }}>
                                            {(qTot.bu / qTot.ac).toFixed(1)} bu/ac weighted
                                          </td>
                                          <td style={{ ...tdS, borderLeft:"1px solid #e4eee4", fontWeight:800 }}>{Math.round(qTot.bu).toLocaleString()}</td>
                                          <td style={{ ...tdS, fontWeight:800 }}>{fmtD0(qTot.cov)}</td>
                                          <td style={{ ...tdS, fontWeight:800, color:"#1f5c2e" }}>{fmtD0(qTot.prem)}</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                  </div>
                                )}
                                {!rolled && qTot.ac > 0 && qTot.ac + 0.05 < dbAc && (
                                  <div style={{ fontSize:10.5, color:"#8a6d1f", marginTop:6 }}>
                                    Total covers the {dbs.filter(d=>d.ac>0).length} database{dbs.filter(d=>d.ac>0).length===1?"":"s"} carrying acres. The rest are priced per acre only — no acreage to total them on.
                                  </div>
                                )}
                                {!rolled && !qTot.ac && (
                                  <div style={{ fontSize:10.5, color:"#8a6d1f", marginTop:6 }}>
                                    {premiumBasis
                                      ? "No acres on any database yet, so there is no policy total — each line is the per-acre quote on the unit's acres."
                                      : "No acres on any database or for the unit yet — each line shows its guarantee and coverage; the premium is pending until acres are entered."}
                                  </div>
                                )}
                                {!rolled && !aphRows.length && (
                                  <div style={{ fontSize:10.5, color:"#677167", marginTop:6 }}>Year records populate on the next sync.</div>
                                )}
                              </div>
                            )}
                            {!open ? (
                              <button onClick={() => set({ open:true, applied: cur?cur.applied:false, rows: rows || seed() })} style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:5, cursor:"pointer", border:"1px solid #cfe0cf", background:"#f4faf4", color:"#1f7a3d" }}>APH database ▸ add {yr1} / edit</button>
                            ) : (
                              <div style={{ border:"1px solid #cfe0cf", borderRadius:9, padding:"10px 12px", background:"#fbfdfb" }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8 }}>
                                  <span style={{ fontSize:10.5, textTransform:"uppercase", letterSpacing:".04em", color:"#60735f", fontWeight:700 }}>APH database (bu/ac) — type {yr1} production, then Update APH</span>
                                  <button onClick={() => set({ open:false })} style={{ border:"none", background:"none", color:"#677167", cursor:"pointer", fontSize:11 }}>collapse ▴</button>
                                </div>
                                <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginBottom:9 }}>
                                  {rows.map((x,i) => (
                                    <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                                      <input type="number" value={x.year} onChange={e => setYr(i, e.target.value)} style={{ ...iC, width:52, color: +x.year===yr1 ? "#1f7a3d" : "#2a3a2a", fontWeight: +x.year===yr1?700:400 }} />
                                      <input type="number" value={x.y} placeholder="bu" onChange={e => setRow(i, e.target.value)} style={iC} />
                                      <button onClick={() => delRow(i)} title="remove" style={{ border:"none", background:"none", color:"#c0392b", cursor:"pointer", fontSize:11, lineHeight:1 }}>✕</button>
                                    </div>
                                  ))}
                                  <button onClick={addRow} disabled={atMax} title={atMax ? "The APH database holds the 10 most recent crop years" : ""} style={{ alignSelf:"flex-start", marginTop:2, fontSize:11.5, fontWeight:700, padding:"4px 9px", borderRadius:5, border:"1px dashed " + (atMax ? "#d5e2d5" : "#9ec49e"), background:"#fff", color: atMax ? "#b7c2b7" : "#1f7a3d", cursor: atMax ? "not-allowed" : "pointer" }}>+ year</button>
                                </div>
                                <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                                  <button onClick={() => set({ applied:true })} style={{ fontSize:12.5, fontWeight:800, padding:"6px 16px", borderRadius:6, border:"1px solid #2d6a2d", background:"#2d6a2d", color:"#fff", cursor:"pointer" }}>Update APH</button>
                                  {edCalc && edCalc.approved != null && <span style={{ fontSize:13 }}><b style={{ color:"#1f5c2e", fontSize:16 }}>{edCalc.approved}</b> bu <span style={{ fontSize:11, fontWeight:700, color:"#b8860b" }}>{aphProvenance({ edited: true }).label}</span> <span style={{ fontSize:11, color:"#60735f" }}>({edCalc.nActual} yrs{edCalc.taBu?`, +${edCalc.taBu} TA`:""}{edCalc.nFloored?`, ${edCalc.nFloored} YA`:""}{edCalc.nExcluded?`, ${edCalc.nExcluded} YE`:""})</span></span>}
                                  {cur && cur.applied && (!edCalc || edCalc.approved == null) && <span style={{ fontSize:11.5, color:"#a5461b" }}>enter at least one yield</span>}
                                  <span style={{ fontSize:10.5, color:"#677167" }}>toggle TA / YA / YE in APH options above · avg + all options are ADM-exact.</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {!inSet ? <div style={{ color:"#a5461b", fontSize:12.5, fontWeight:600 }}>⚠ {capCty(r.county)}, {unitState(r)} isn't in the 2027 wheat rate set yet — can't quote. {STATE_FIPS[unitState(r)] ? "New county for a covered state — flag it for the rate tables." : `${unitState(r)} isn't loaded (we cover NY/PA/MD/OH).`}</div> : (<>
                        <div style={{ overflowX:"auto" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:640 }}>
                            <thead>
                              <tr>
                                <th style={{ padding:"6px 8px", textAlign:"left" }}></th>
                                {YRS.map((y,i) => {
                                  const mb = a => ({ fontSize:10.5, fontWeight:700, padding:"1px 5px", borderRadius:4, cursor:"pointer", border:"1px solid "+(a?"#2d6a2d":"#cfd9cf"), background:a?"#dbeedb":"#fff", color:a?"#1f7a3d":"#8a978a", lineHeight:1.5, display:"inline-block" });
                                  return (
                                  <th key={i} colSpan={2} style={{ ...colHead, textAlign:"center", verticalAlign:"top", background:y.bg, color:y.col, borderLeft:"2px solid #e4ede4", padding:"6px 6px" }}>
                                    <div>{y.lbl}</div>
                                    {y.editable ? (
                                      <div style={{ fontSize:10, fontWeight:700, color:"#8aa08a", margin:"3px 0 2px", letterSpacing:".03em" }}>set in Coverage above</div>
                                    ) : <div style={{ fontWeight:500, color:"#677167", fontSize:10 }}>{y.sub}</div>}
                                    <div style={{ fontWeight:500, color:"#60735f", fontSize:10.5, marginTop:3 }}>APH {y.aphY==null?"—":y.aphY.toFixed(1)} · {y.guar==null?"—":y.guar.toFixed(1)} bu · {y.price==null?"—":$ac(y.price)}/bu</div>
                                  </th>
                                );})}
                              </tr>
                              <tr>
                                <th style={{ padding:"2px 8px 6px", textAlign:"left", fontSize:10, textTransform:"uppercase", letterSpacing:".03em", color:"#677167", fontWeight:700 }}>$/ac</th>
                                {YRS.map((y,i) => <React.Fragment key={i}>
                                  <th style={{ ...colHead, fontSize:10, color:"#60735f", background:y.bg, borderLeft:"2px solid #e4ede4" }}>Prem</th>
                                  <th style={{ ...colHead, fontSize:10, color:"#677167", background:y.bg }}>Cov</th>
                                </React.Fragment>)}
                              </tr>
                            </thead>
                            <tbody>
                              {chainRow("Production guarantee /ac", pgOf)}
                              {chainRow("Revenue guarantee /ac", rgOf)}
                              {prodRow("MPCI", "mpci")}
                              {prodRow("SCO", "sco")}
                              {prodRow("ECO", "eco")}
                              {prodRow("Total", "tot", true)}
                              {/* PREVENTED PLANTING — coverage column only, and deliberately
                                  BELOW the total. PP is already paid for inside the MPCI
                                  premium above; only the PF buy-up costs extra, and PF is not
                                  offered on 2027 fall wheat. Putting a premium figure beside
                                  it, or folding it into Total, would read as another layer the
                                  grower has to buy on top — it isn't, and it doesn't stack
                                  with the planted guarantee either: an acre is planted or
                                  prevented, never both. */}
                              {ppF.base != null && prodRow(Math.abs(prevPpF - ppF.base) > 1e-9
                                ? `Prevent plant · ${MPCI_WHEAT_YEAR - 1}: ${Math.round(prevPpF*100)}% · ${MPCI_WHEAT_YEAR}: ${Math.round(ppF.base*100)}% of guarantee`
                                : `Prevent plant · ${Math.round(ppF.base * 100)}% of guarantee`, "pp")}
                            </tbody>
                          </table>
                        </div>
                        {curTot > 0 && (() => {
                          const dSame = sameTot - curTot, dRec = recTot - curTot;
                          const dv = d => (d >= 0 ? "+" : "−") + "$" + Math.abs(Math.round(d * 100) / 100).toFixed(2);
                          return <div style={{ fontSize:11.5, color:"#5c6f5c", marginTop:7 }}>
                            vs 2026 · same coverage <b style={{ color: dSame > 0 ? "#a5461b" : "#1f7a3d" }}>{dv(dSame)}/ac</b> ({dv(dSame * acres)} total) · {recLbl} <b style={{ color: dRec > 0 ? "#a5461b" : "#1f7a3d" }}>{dv(dRec)}/ac</b>
                          </div>;
                        })()}
                        {rq && (
                          <div style={{ margin:"14px 0 4px" }}>
                            <div style={{ fontSize:10.5, color:"#60735f", fontWeight:700, textTransform:"uppercase", letterSpacing:".04em", marginBottom:7 }}>Your economics &amp; stress test — {recLbl} ({Math.round(rc.cl*100)}% {unitLbl[rc.unit]}{rc.sco?" +SCO":""}{rc.eco?" +ECO":""})</div>
                            <QuoteEconomics crop="Wheat" aph={aph} rateYield={rateY} acres={acres} price={price}
                              cl={rc.cl} guarBu={rq.guarBu} premAc={rq.netAcre}
                              sco={rSco ? { covPerAc: rSco.covPerAc, premAc: rSco.netAcre } : null}
                              eco={rEco ? { covPerAc: rEco.covPerAc, premAc: rEco.netAcre } : null}
                              corr={rec ? rec.corr : null} />
                          </div>
                        )}
                        {rq && (
                          <div style={{ margin:"14px 0 4px" }}>
                            <div style={{ fontSize:10.5, color:"#60735f", fontWeight:700, textTransform:"uppercase", letterSpacing:".04em", marginBottom:7 }}>Coverage stack — {recLbl} ({Math.round(rc.cl*100)}% {unitLbl[rc.unit]}{rc.sco?" +SCO":""}{rc.eco?" +ECO":""})</div>
                            {(() => {
                              // One bar per level worth comparing: `chartLv` — the agent's own
                              // selection for this unit, or the automatic set where they have not
                              // chosen. Every bar carries the RECOMMENDED endorsement election so
                              // the only variable moving across the row is the MPCI level itself.
                              // What he actually PAID last year, shown under the last-year bar so
                              // "you paid this, the same coverage costs that" is on the chart itself.
                              // It is the AIP's own producer premium, never a re-quote.
                              //
                              // Every bar carries the RECOMMENDED endorsements, so if last year's mix
                              // differed the two figures are not like-for-like and the note says so.
                              // Without it a grower who is being sold SCO this year reads the jump as
                              // a rate increase, when he is really buying something he did not have.
                              const had26 = [curSco ? "SCO" : null, curEco ? "ECO" : null].filter(Boolean).join("+") || "no endorsements";
                              const nowMix = [rc.sco ? "SCO" : null, rc.eco ? "ECO" : null].filter(Boolean).join("+") || "no endorsements";
                              const paidNote = had26 === nowMix ? null : `2026 was ${Math.round(curCl * 100)}% ${unitLbl[curUnit]} · ${had26}`;
                              const lv = chartLv;
                              const bars = lv.map(cl => {
                                const m = quoteRP({ ...bfrQ, state:fips, county, aph, rateYield:rateY, adjustedYield:adjY, cl, acres, unit:rc.unit, price:+price, vol:+vol, practice:uPrac });
                                if (!m) return null;
                                const sc = rc.sco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"SCO", baseCl:cl, aph, acres, price:+price, vol:+vol, coveragePct: areaCoverageFractionOfPct(rc.scoPct), practice:uPrac }) : null;
                                const ec = rc.eco ? quoteAreaRP2027({ ...bfrQ, state:fips, county, endorsement:"ECO", baseCl:cl, aph, acres, price:+price, vol:+vol, coveragePct: areaCoverageFractionOfPct(rc.ecoPct), practice:uPrac }) : null;
                                const prem = (m.netAcre||0) + (sc?sc.netAcre:0) + (ec?ec.netAcre:0);
                                const cov  = (m.liabPerAc||0) + (sc?sc.covPerAc:0) + (ec?ec.covPerAc:0);
                                const isRec = Math.abs(cl - rc.cl) < 1e-9, isLast = curCl != null && Math.abs(cl - curCl) < 1e-9;
                                // Ratio is MPCI-ONLY: its own coverage over its own premium.
                                // Blending the endorsements in made it a number about the bundle
                                // rather than about the level being chosen, and SCO/ECO ride at a
                                // very different price per dollar so they dominated the movement.
                                return { cl, m, sc, ec, prem, cov,
                                         ratio: (m.netAcre > 0 && m.liabPerAc > 0) ? m.liabPerAc / m.netAcre : null, isRec, isLast,
                                         tag: isRec ? (recOverridden ? "sel" : "rec") : isLast ? "elected" : null, year: 2027 };
                              }).filter(Boolean);
                              // LAST YEAR GETS ITS OWN BAR, built from the AIP’s actual 2026 figures
                              // rather than a re-quote. Carrying it as a footnote under the 2027 bar
                              // at the same level did not answer the question being asked — you cannot
                              // see what he bought, only what it would cost now.
                              //
                              // It is drawn against LAST YEAR’s expected revenue (2026 APH x 2026
                              // price), because that is the revenue those layers actually sat on. Each
                              // bar is normalised to its own 100%, so the shapes stay comparable while
                              // the dollars underneath carry the real change.
                              // ⚠ AN ELECTION WITH NO ACRES STILL HAS A COVERAGE, AND THAT BAR IS
                              // WORTH DRAWING. Kevin, 2026-09-02: "I still want to see the bar and
                              // what their elected coverage wouldve cost last year even though they
                              // didnt plant any." This was gated on `curTot > 0` — the premium the
                              // AIP BILLED — so a county the grower elected and then did not plant
                              // drew nothing at all, and the 2027 bars stood beside a blank.
                              //
                              // The COVERAGE half needs no rate table: it is the grower's own
                              // approved yield x his elected level x that year's price, and the
                              // SCO/ECO layers are their bands of the same expected revenue on the
                              // 2026 lattice (86/95, via scoEcoBands). Every figure is exact.
                              //
                              // ⚠ THE PREMIUM HALF IS REFUSED, AND THIS IS WHY. Quoting what it
                              // WOULD have cost needs 2026 rate tables. `tools/mpci-quoter/b2026`
                              // looks like a candidate and is not one: its own header claims the
                              // 2027 ADM, and driven against a premium the AIP actually billed —
                              // KAST FARMS, Orleans NY, EU 80%, approved 79 / rate 71 / adjusted 71,
                              // options PF,QL,TA,YA,YC — it returns a premium rate of 0.108967
                              // against the billed 0.132117: 17.5% light, with the rate and adjusted
                              // yields and the PF option rate all supplied. The repo's own harness
                              // (backtest_book.mjs) cannot adjudicate it either: it needs a
                              // 2026_0011_rates.json extraction that is not in the tree.
                              // So the layer renders its coverage and NO premium, and says so. A
                              // 17.5% error sitting beside penny-exact 2027 figures, on a document
                              // a grower signs, is worse than an empty column.
                              const billed26 = curTot > 0;
                              const b26Bands = scoEcoBands(2026);
                              const er2026 = (aph2026 > 0 && p2026 > 0) ? aph2026 * p2026 : null;
                              const covElect = (er2026 != null && curCl != null) ? er2026 * curCl : null;
                              const scoElect = (er2026 != null && curCl != null) ? er2026 * (b26Bands.scoTop - curCl) : null;
                              const ecoElect = (er2026 != null) ? er2026 * (b26Bands.ecoTop - b26Bands.ecoBottom) : null;
                              const lastBar = (curCl != null && aph2026 && p2026 && (billed26 || covElect != null)) ? {
                                cl: curCl, year: 2026, tag: "last",
                                aph: aph2026, price: p2026,
                                // A BILLED figure always wins; the derived one fills only where
                                // nothing was billed, so a planted county is untouched by this.
                                m: { netAcre: billed26 ? curMpci : null, liabPerAc: (billed26 && cov2026 != null) ? cov2026 : covElect },
                                sc: curSco ? { netAcre: billed26 ? curScoAc : null, covPerAc: (billed26 && curScoCov != null) ? curScoCov : scoElect } : null,
                                ec: curEco ? { netAcre: billed26 ? curEcoAc : null, covPerAc: (billed26 && curEcoCov != null) ? curEcoCov : ecoElect } : null,
                                prem: billed26 ? curTot : null,
                                cov: billed26
                                  ? (cov2026 || 0) + (curScoCov || 0) + (curEcoCov || 0)
                                  : (covElect || 0) + (curSco ? (scoElect || 0) : 0) + (curEco ? (ecoElect || 0) : 0),
                                notBilled: !billed26,
                              } : null;
                              if (lastBar) lastBar.ratio = (lastBar.m.netAcre > 0 && lastBar.m.liabPerAc > 0) ? lastBar.m.liabPerAc / lastBar.m.netAcre : null;
                              // LAST YEAR IN BUSHELS, THROUGH THE SHARED RULE. priorYearChain is the one
                              // definition of "the AIP billed liability over the price, else APH x level" —
                              // the printed sheet already calls it, and a second copy here is how the two
                              // come to disagree about one farm. A year it cannot resolve stays null and the
                              // bar simply carries no bushel line.
                              if (lastBar) lastBar.guarBu = priorYearChain({ publishedPrice: p2026,
                                liabPerAc: lastBar.m.liabPerAc, aph: aph2026, cl: curCl }).guarBu;
                              // Premium is the variable that actually moves across levels — coverage
                              // is near-flat because SCO backfills whatever MPCI does not cover up to
                              // 90%, so all three totals land within a dollar of each other. Scaling a
                              // premium bar across the set puts that difference where the eye can read
                              // it, rather than asking a 5-point band shift to carry the whole story.
                              const allBars = [...(lastBar ? [lastBar] : []), ...bars];
                              const premScale = Math.max(0, ...allBars.map(b => b.prem || 0));
                              // The recommended bar's own MPCI coverage + total premium, so every
                              // other bar can state what moving to it transfers and what it costs.
                              const recB = bars.find(x => x.isRec) || null;
                              const recBarFigs = recB ? { mpciCov: recB.m.liabPerAc, prem: recB.prem } : null;
                              return (
                                <>
                                <div style={{ display:"flex", gap:14, flexWrap:"nowrap", alignItems:"stretch", width:"100%", padding:"4px 2px 2px" }}>
                                  {/* ⚠ NAME WHAT IS ACTUALLY MISSING, and derive it from the same values
                                      the bar is gated on. This note read "no elected level and no approved
                                      yield" WHATEVER the cause — and stood beside a bar the very next
                                      column labels "his election", contradicting itself on screen.
                                      CRIPPLE CREEK / WYOMING is the measured case: his CY2026 row carries
                                      the election (75%) AND the approved yield (61 bu), and has acres,
                                      liability, premium and price_election ALL null. What the bar lacks
                                      is a PRICE, not an election. A message stating two causes that are
                                      both false sends the reader to look at the wrong column — the row
                                      label two lines up already says "no acres reported, nothing billed",
                                      so the app knew and this note disagreed with it. */}
                                  {!lastBar && curCl != null && (() => {
                                    const yr = MPCI_WHEAT_YEAR - 1;
                                    const why = !(aph2026 > 0)
                                      ? "No approved yield is on file for " + yr + ", so there is no guarantee to draw a stack from."
                                      : !(p2026 > 0)
                                        ? "His " + yr + " election (" + Math.round(prevCl*100) + "%) and approved yield (" + aph2026.toFixed(1) + " bu) ARE on file — what is missing is the PRICE: that row carries no price election, and no billed liability to back one out of, so the layers cannot be sized."
                                        : "The " + yr + " coverage could not be priced from what is on file.";
                                    return (
                                      <div style={{ alignSelf:"center", maxWidth:150, fontSize:11, color:"#8a6100", background:"#fdf9ec", border:"1px dashed #e8d5a8", borderRadius:9, padding:"10px 11px", lineHeight:1.45 }}>
                                        <b>No {yr} bar.</b> {why}
                                      </div>
                                    );
                                  })()}
                                  {allBars.map(b => (
                                    <CoverageStackViz key={b.year + "|" + b.cl} compact bandYear={b.year} cl={b.cl}
                                      aph={b.year === 2026 ? b.aph : aph} price={b.year === 2026 ? b.price : price}
                                      caption={Math.round(b.cl*100) + "%"} tag={b.tag} ratio={b.ratio}
                                      guideAt={b.year === 2026 ? null : rc.cl}
                                      yearLabel={b.year === 2026 ? (b.notBilled ? "2026 elected" : "2026 actual") : null} premScale={premScale}
                                      paidNote={b.year === 2026 ? paidNote : null}
                                      vsRec={recBarFigs}
                                      guarBu={b.guarBu != null ? b.guarBu : (b.m ? b.m.guarBu : null)}
                                      mpci={{ prem: b.m.netAcre, cov: b.m.liabPerAc }}
                                      sco={b.sc ? { prem: b.sc.netAcre, cov: b.sc.covPerAc } : null}
                                      eco={b.ec ? { prem: b.ec.netAcre, cov: b.ec.covPerAc } : null} />
                                  ))}
                                </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                        <div style={{ fontSize:10.5, color:"#60735f", fontWeight:700, textTransform:"uppercase", letterSpacing:".04em", margin:"12px 0 4px" }}>All coverage options — {capCty(r.county)} ($/ac net)</div>
                        {quoteGrid(fips, county, aph, quoteAcres, price, vol, { cl: curCl, unit: curUnit }, rc.scoPct, rc.ecoPct, { quoteRP, premiumBasis, practice: uPrac, unit: rc.unit, sco: rc.sco, eco: rc.eco, rateYield: rateY, adjustedYield: adjY, pinned: chartLv, onPin: toggleChartLevel, chartLevels: chartLv, onChartLevel: toggleChartLevel })}
                        {/* ── THE COVERAGE-LEVEL OPTIMIZER, DEFAULT OFF ──────────────────────
                            Kevin's scope: a toggle, strictly dollars and cents, underlying MPCI
                            only. It answers a different question from the recommendation panel
                            below it — not "what should this renewal do" but "what would every
                            level have collected on this farm's own record" — so it is its own
                            panel behind a control rather than more rows in that one.

                            ⚠ IT IS DELIBERATELY *OUTSIDE* THE `rec &&` GUARD. `rec` is null
                            whenever `premiumBasis` is false, i.e. `acres > 0 && !acreageAssumed`
                            — and CY2027 wheat acreage is not reported until 15 November, so
                            gating on it would have hidden this control on essentially every
                            real wheat card between now and then. The optimizer does not need
                            the quote year's acreage: it prices each replayed year on the acres
                            the AIP ALREADY reported for that database in that year.

                            Off by default means the price history is fetched only for a card
                            somebody opened it on. */}
                        {inSet && (() => {
                          const opx = pxFor(uPrac, FIPS_STATE[fips]);
                          const optQuotePrice = opx && +opx.price > 0 ? +opx.price : null;
                          return (
                            <div style={{ marginTop:10 }}>
                              <label style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:10.5,
                                              color:"#3a6a3a", cursor:"pointer" }}>
                                <input type="checkbox" checked={optOn} onChange={(e) => setOptOn(e.target.checked)} />
                                Coverage level optimizer — replay every level against this farm's own yield record
                              </label>
                              {optOn && (
                                <LevelOptimizerPanel
                                  dbRows={aphRows}
                                  priceRows={optPx}
                                  county={county}
                                  practice={uPrac}
                                  state={FIPS_STATE[fips]}
                                  unitStructure={(cardUnitSrc && cardUnitSrc.unit) || null}
                                  quoteRP={quoteWheatRP2027}
                                  quotePrice={optQuotePrice}
                                  incumbentLevel={recIncCl}
                                  loading={!optPx && !optErr}
                                  error={optErr}
                                />
                              )}
                            </div>
                          );
                        })()}
                        {rec && (() => {
                          // ── THE CARD LEADS WITH THE MARGINAL COST ──────────────────
                          // Not CV, not a correlation. Raising the level does not buy more
                          // total protection — it moves a 5-point band off the county
                          // trigger onto the farm's own — so the two numbers that decide it
                          // are what that move costs and what it is worth. Everything else
                          // is evidence behind those two and goes in the drawer.
                          const d2 = n => n == null ? "—" : "$" + (Math.round(Math.abs(n)*100)/100).toFixed(2);
                          const sgn = n => n == null ? "—" : (n > 0 ? "+" : n < 0 ? "−" : "") + d2(n);
                          const f = rec.fit || {}, nd = rec.need || {};
                          const up = rec.moves && rec.moves.up, dn = rec.moves && rec.moves.down;
                          // WARNING: THE TILE MUST EXPLAIN THE DECISION THAT WAS ACTUALLY MADE.
                          // These tiles rendered rec.cases - the OLD county-miss-rate
                          // valuation - while the level came from rec.bandDecision, which
                          // values a band on the FARM's own yield record and never reads
                          // the county at all. So a card could recommend 85% with "Value
                          // not measurable - the county miss rate is not measured" printed
                          // directly above it. Kevin, 2026-09-10: "Silas seems to
                          // contradict itself in the explanation, or at least does not do
                          // a good job justifying 85% coverage." It was not a wrong number;
                          // it was the wrong engine's sentence.
                          //
                          // rec.cases is deliberately still RETURNED and is still read by
                          // nothing on this card - the area-fit evidence it carries is
                          // rendered on its own row below, where it is true.
                          const bandOf = (from) => ((rec.bandDecision && rec.bandDecision.bands) || [])
                            .find((b) => Math.abs(b.from - from) < 1e-9);
                          const bu = bandOf(0.80), bdn = bandOf(0.75);
                          const fitCol = f.fit === WHEAT_FIT.STRONG ? "#1f7a3a"
                            : f.fit === WHEAT_FIT.WEAK ? "#a5561d"
                            : f.fit === WHEAT_FIT.INSUFFICIENT ? "#60735f" : "#2d6a4a";
                          const Move = ({ label, mv, bd }) => !mv ? null : (
                            <div style={{ flex:"1 1 230px", border:"1px solid #d8e6d8", borderRadius:8, background:"#fff", padding:"8px 10px" }}>
                              <div style={{ fontSize:11, fontWeight:800, color:"#1f3a25" }}>{label}</div>
                              <div style={{ fontSize:19, fontWeight:800, color: mv.netCostPerAc > 0 ? "#a5561d" : "#1f7a3a", margin:"2px 0 1px" }}>
                                {sgn(mv.netCostPerAc)}<span style={{ fontSize:11, fontWeight:600 }}>/ac</span>
                              </div>
                              <div style={{ fontSize:10.5, color:"#5a6a5a", lineHeight:1.4 }}>
                                Moves another 5% of revenue protection onto your farm's individual trigger.
                              </div>
                              <div style={{ fontSize:10.5, color:"#60735f", marginTop:4, lineHeight:1.45 }}>
                                MPCI {sgn(mv.mpciDelta)} · SCO you stop paying {sgn(mv.scoDisplaced)}
                                {mv.overstatementPerAc > 0 ? " · a raw MPCI comparison overstates this by " + d2(mv.overstatementPerAc) : ""}
                              </div>
                              {bd && bd.priceable && (
                                <div style={{ fontSize:10.5, color:"#5a6a5a", marginTop:3 }}>
                                  Worth {d2(bd.expIndemnity)}/ac on your own record
                                  {" — " + bd.yearsPaying + " of " + bd.nYears + " years"}
                                  {bd.ratio != null ? " (" + bd.ratio.toFixed(2) + "× its cost)" : " (free net of SCO)"}
                                </div>
                              )}
                              {/* The evidence, in the same terms the decision used. A band
                                  that covers its cost but not the top level's margin is a
                                  DIFFERENT statement from one that never covered it, and
                                  saying the second where the first is true is how this
                                  card came to contradict itself. */}
                              {bd && bd.priceable && bd.marginShort && (
                                <div style={{ fontSize:10.5, color:"#a5561d", marginTop:3 }}>
                                  Covers its cost, but not by the {bd.bar}× margin the top level is held to
                                  {bd.looMin != null ? " — falls to " + bd.looMin.toFixed(2) + "× if a single year is dropped" : ""}
                                </div>
                              )}
                              {bd && bd.priceable && !bd.marginShort && bd.evidence === "CLEARLY_SUPPORTS" && (
                                <div style={{ fontSize:10.5, color:"#1f7a3a", marginTop:3 }}>
                                  Clears its own cost, and still does with any single year dropped
                                  {bd.looMin != null ? " (" + bd.looMin.toFixed(2) + "–" + bd.looMax.toFixed(2) + "×)" : ""}
                                </div>
                              )}
                              {bd && bd.priceable && !bd.marginShort && bd.evidence === "BORDERLINE" && (
                                <div style={{ fontSize:10.5, color:"#a5561d", marginTop:3 }}>
                                  Too close to call — the answer reverses if a single year is dropped
                                  {bd.looMin != null ? " (" + bd.looMin.toFixed(2) + "–" + bd.looMax.toFixed(2) + "×)" : ""}
                                </div>
                              )}
                              {bd && bd.priceable && bd.evidence === "CLEARLY_DOES_NOT_SUPPORT" && (
                                <div style={{ fontSize:10.5, color:"#a5561d", marginTop:3 }}>
                                  Has not covered its cost on this farm's record
                                </div>
                              )}
                              {bd && !bd.priceable && (
                                <div style={{ fontSize:10.5, color:"#60735f", marginTop:3 }}>Not rated for this county and practice</div>
                              )}
                              {!bd && (
                                <div style={{ fontSize:10.5, color:"#60735f", marginTop:3 }}>
                                  {rec.premiumWithheld ? "Needs a planted acreage before this band can be priced"
                                    : "Not enough yield history to test this band"}
                                </div>
                              )}
                            </div>
                          );
                          return (
                            <div style={{ marginTop:12, border:"1px solid #cfe3cf", borderRadius:10, background:"#f4faf4", padding:"11px 13px" }}>
                              {/* THE RENEWAL DECISION LEADS. Where the grower already
                                  holds an election the question is whether to CHANGE it,
                                  so the headline is the classification, not a level the
                                  engine picked from scratch. */}
                              <div style={{ fontSize:13, fontWeight:800, marginBottom:2,
                                            // ⚠ AMBER IS THE "here is a change" COLOUR, and
                                            // ACREAGE_REQUIRED fell into it through the else
                                            // branch — so "Planned acreage needed" was painted the
                                            // same as "Consider 80%". It is a MISSING INPUT, not a
                                            // recommendation; it reads grey with the other state we
                                            // cannot answer from. Today that is EVERY CY2027 unit,
                                            // because acreage is reported in November.
                                            color: rec.classification === WHEAT_CLASS.KEEP ? "#1f5c2e"
                                              : (rec.classification === WHEAT_CLASS.INSUFFICIENT
                                                 || rec.classification === WHEAT_CLASS.ACREAGE) ? "#60735f" : "#8a5a00" }}>
                                {/* ⚠ NOT `WHEAT_CLASS_LABEL[c] || "Recommended"`. The ladder
                                    is the incumbent's own neighbourhood now, so a 65% call
                                    carries the classification CONSIDER_65, which is not in
                                    that map — it fell through to the bare word "Recommended"
                                    and the level vanished off the headline. One reader for
                                    the fixed values and the generated ones. */}
                                {wheatClassLabel(rec.classification)}
                                {rec.incumbentLevel != null && !rec.isChange
                                  ? " — " + Math.round(rec.incumbentLevel*100) + "% " + (rec.unit==="EU"?"Enterprise":"Basic") + " RP"
                                  : ": " + Math.round(rec.cl*100) + "% " + (rec.unit==="EU"?"Enterprise":"Basic") + " RP"}
                                {rec.sco?" + SCO":""}{rec.eco?" + ECO95":""}
                                <span style={{ fontWeight:600, color:"#3a6a3a", fontSize:11.5 }}> · {$ac(rec.netAc)}/ac</span>
                              </div>
                              <div style={{ fontSize:10.5, color:"#5a6a5a", marginBottom:7 }}>
                                {/* ⚠ NAME THE LEVELS ACTUALLY COMPARED. This said "80% is the
                                    benchmark it is measured against" for every renewal, which
                                    was true while the set was a fixed 75/80/85. The ladder is
                                    the incumbent's own neighbourhood now, so for a 60% grower
                                    80% is not priced at all and `rec.benchmark` is null —
                                    the sentence would have named a comparison the card did
                                    not make. */}
                                {/* ⚠ "Compared against 70% · 75% · 80% · 85%" CLAIMED A COMPARISON
                                    THAT DID NOT HAPPEN. Those levels are on the ladder, but with no
                                    acreage every one of them prices to null — the engine says so
                                    itself ("the premium comparison it is judged on cannot be
                                    computed without an acreage") and classifies ACREAGE_REQUIRED.
                                    Listing them reads as four priced alternatives that were weighed
                                    and lost. Same class as the strategy report printing "Keep
                                    current coverage" over a comparison it never ran. */}
                                {rec.classification === WHEAT_CLASS.ACREAGE
                                  ? "Enter this unit's planned acres to price the comparison. Until then the level shown is your current election, not a recommendation — the premium every alternative is judged on divides by acres."
                                  : rec.incumbentLevel != null
                                  ? "Baseline is your current " + Math.round(rec.incumbentLevel*100) + "% election, and a change has to earn it. Compared against "
                                    + (rec.levels || []).map((l) => Math.round(l.cl*100) + "%").join(" · ")
                                    + (rec.benchmark ? "; the 80% benchmark is " + $ac(rec.benchmark.totalNetAcre) + "/ac" : "") + "."
                                  : "No current election on file — 80% is the starting benchmark and 75/85 are evaluated from there."}
                              </div>
                              {rec.keepReason && (
                                <div style={{ fontSize:11, color:"#5a6a5a", marginBottom:7, fontStyle:"italic" }}>{rec.keepReason}.</div>
                              )}
                              {/* THIS PANEL IS THE ENGINE'S LEVEL COMPARISON, priced with SCO and ECO at
                                  the full band. Beside a column quoting a reduced election its totals
                                  differ from the quote above, so it says which basis it is on rather
                                  than leaving two totals for one stack on one card. */}
                              {((rc.sco && +rc.scoPct < 100) || (rc.eco && +rc.ecoPct < 100)) && (
                                <div style={{ fontSize:10.5, color:"#8a5a00", marginBottom:7 }}>
                                  Figures in this comparison price SCO and ECO at 100% of each band. The quote above prices {[rc.sco && +rc.scoPct < 100 ? areaCoverageLabel("SCO", rc.scoPct) : null, rc.eco && +rc.ecoPct < 100 ? areaCoverageLabel("ECO", rc.ecoPct) : null].filter(Boolean).join(" and ")}.
                                </div>
                              )}

                              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8 }}>
                                <Move label="75% → 80%" mv={dn} bd={bdn} />
                                <Move label="80% → 85%" mv={up} bd={bu} />
                              </div>

                              <div style={{ fontSize:11.5, lineHeight:1.5, borderTop:"1px solid #e0ece0", paddingTop:6 }}>
                                <div style={{ marginBottom:2 }}><b>Why not 85%?</b>{" "}
                                  {rec.whyNot85 ? rec.whyNot85 + "." : <span style={{ color:"#1f7a3a" }}>It is the recommendation.</span>}</div>
                                <div><b>Why not 75%?</b>{" "}
                                  {rec.whyNot75 ? rec.whyNot75 + "." : <span style={{ color:"#1f7a3a" }}>It is the recommendation.</span>}</div>
                              </div>
                              {rec.downUncertaintyWarning && (
                                <div style={{ fontSize:10.5, color:"#a5561d", marginTop:5, fontWeight:600 }}>⚠ {rec.downUncertaintyWarning}</div>
                              )}
                              {rec.conflict && (
                                <div style={{ fontSize:10.5, color:"#a5561d", marginTop:5, fontWeight:600 }}>⚠ {rec.conflict}</div>
                              )}

                              <div style={{ display:"flex", gap:14, flexWrap:"wrap", fontSize:11, marginTop:8, borderTop:"1px solid #e0ece0", paddingTop:6 }}>
                                <span>Farm risk <b>{WHEAT_NEED_LABEL[nd.need] || "—"}</b>
                                  {nd.lossFreq80 != null ? " · below 80% APH in " + Math.round(nd.lossFreq80*100) + "% of " + nd.yearsObserved + " yrs" : ""}</span>
                                <span>Area fit <b style={{ color:fitCol }}>{rec.fitLabel || "—"}</b>
                                  {f.hitRate != null ? " · county paid in " + f.coveredYears.length + " of " + f.nLossYears + " loss yrs" : " · not measured"}</span>
                                {/* Kevin, 2026-09-10: "we should also show the Farmers APH CV
                                    and County Basis on the quote to help explain why we are
                                    recommending what we are." Both were already computed and
                                    printed on the Coverage Advisor's county book; the quote
                                    showed the two LABELS above and neither figure.
                                    CV is the farm's own yield volatility - the higher it is,
                                    the more often its own record falls into a buy-up band.
                                    Basis is APH ÷ the county's expected yield: near 1.0 the
                                    farm moves with its county, so an AREA product tracks it.
                                    Both are CONTEXT for the bands above, not inputs to them,
                                    and neither is given a verdict colour. */}
                                {nd.cv != null && (
                                  <span>APH CV <b>{nd.cv.toFixed(0)}%</b>
                                    <span style={{ color:"#60735f" }}>{" · own-yield volatility over " + nd.yearsObserved + " yrs"}</span></span>
                                )}
                                <span>County basis {f.basis != null
                                  ? <><b>{f.basis.toFixed(2)}×</b><span style={{ color:"#60735f" }}>
                                      {" · APH " + Math.round(rec.need && rec.need.aph ? rec.need.aph : 0) + " vs " + Math.round(f.countyExpected) + " bu county expected" + (f.countyExpectedYear ? " (" + f.countyExpectedYear + ")" : "")}</span></>
                                  : <b style={{ color:"#60735f" }}>not measured</b>}</span>
                              </div>

                              {!!(rec.levels && rec.levels.length) && (
                                <div style={{ marginTop:8, borderTop:"1px solid #e0ece0", paddingTop:6, overflowX:"auto" }}>
                                  <div style={{ fontSize:10, color:"#60735f", fontWeight:700, textTransform:"uppercase", letterSpacing:".04em", marginBottom:3 }}>The stack, side by side ($/ac producer premium)</div>
                                  <table style={{ width:"100%", fontSize:11, borderCollapse:"collapse", minWidth:520 }}>
                                    <thead><tr style={{ color:"#60735f", textAlign:"right" }}>
                                      <th style={{ textAlign:"left", fontWeight:600 }}>Structure</th>
                                      <th style={{ fontWeight:600 }}>MPCI</th><th style={{ fontWeight:600 }}>SCO</th><th style={{ fontWeight:600 }}>ECO95</th>
                                      <th style={{ fontWeight:600 }}>Total</th><th style={{ fontWeight:600 }}>Your trigger</th><th style={{ fontWeight:600 }}>County trigger</th>
                                    </tr></thead>
                                    <tbody>
                                      {rec.levels.map(l => {
                                        const st = l.stacks.RP_SCO_ECO || l.stacks.RP_SCO || l.stacks.RP_ECO || l.stacks.RP;
                                        if (!st) return null;
                                        const isRec = Math.abs(l.cl - rec.cl) < 1e-9;
                                        return (
                                          <tr key={l.cl} style={{ background: isRec ? "#e6f4e6" : "transparent", textAlign:"right" }}>
                                            <td style={{ textAlign:"left", padding:"2px 0", fontWeight: isRec?700:500 }}>
                                              {Math.round(l.cl*100)}% RP{l.scoBand?" + SCO":""}{l.ecoBand?" + ECO95":""}{isRec?" ←":""}
                                            </td>
                                            <td>{d2(st.mpciNetAcre)}</td><td>{st.scoNetAcre ? d2(st.scoNetAcre) : "—"}</td>
                                            <td>{st.ecoNetAcre ? d2(st.ecoNetAcre) : "—"}</td>
                                            <td style={{ fontWeight:700 }}>{d2(st.totalNetAcre)}</td>
                                            <td>{d2(st.individualLiabPerAc)}</td><td>{d2(st.areaProtPerAc)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {rec.basisRisk && (
                                <div style={{ marginTop:8, background:"#fff8ec", border:"1px solid #e8d9b8", borderRadius:8, padding:"7px 9px", fontSize:10.5, color:"#6a5326", lineHeight:1.5 }}>
                                  <b>This is not {rec.basisRisk.areaTopPct}% individual coverage.</b> {rec.basisRisk.note}{" "}
                                  {rec.basisRisk.timingNote} {rec.basisRisk.ppNote}
                                </div>
                              )}

                              {!!rec.endorsements && (
                                <div style={{ display:"flex", gap:14, flexWrap:"wrap", fontSize:11, marginTop:7, borderTop:"1px solid #e0ece0", paddingTop:6 }}>
                                  {["sco","eco"].map(k => {
                                    const e = rec.endorsements[k];
                                    if (!e || !e.offered) return <span key={k} style={{ color:"#60735f" }}>{k === "sco" ? "SCO" : "ECO95"}: not offered here</span>;
                                    return (<span key={k}>{k === "sco" ? "SCO" : "ECO95"}: <b>{WHEAT_ECON_LABEL[e.grade]}</b> economics
                                      {e.ratio != null ? " (" + e.ratio + ":1 cover per premium $)" : ""}
                                      {e.recommended ? "" : " — not recommended"}</span>);
                                  })}
                                  <span style={{ color:"#60735f" }}>Area fit is judged separately, above.</span>
                                </div>
                              )}

                              <details style={{ marginTop:7 }}>
                                <summary style={{ cursor:"pointer", fontSize:10.5, color:"#4a7a4a", fontWeight:600 }}>Evidence and method</summary>
                                <ul style={{ margin:"6px 0 6px", paddingLeft:17, fontSize:11.5, color:"#3a4a3a", lineHeight:1.5 }}>
                                  {rec.why.map((w, i) => <li key={i} style={{ marginBottom:3 }}>{w}</li>)}
                                </ul>
                                <div style={{ fontSize:10, color:"#60735f", display:"flex", gap:12, flexWrap:"wrap", borderTop:"1px solid #e0ece0", paddingTop:5 }}>
                                  <span>area index: RMA county final yield + settled revenue ratio{f.areaYearRange?" · " + f.areaYearRange[0] + "–" + f.areaYearRange[1]:""}</span>
                                  {rec.corr != null && <span>corr <b>{rec.corr.toFixed(2)}</b> over {rec.corrN} overlapping yrs</span>}
                                  <span>SCO tops at {WHEAT_SCO_TOP}% for CY2027; ECO {WHEAT_SCO_TOP}→95%</span>
                                  {nd.basisNote && <span>{nd.basisNote}</span>}
                                  {!!(f.reasons && f.reasons.length) && <span style={{ color:"#a5561d" }}>{f.reasons.join("; ")}</span>}
                                  {!!(nd.reasons && nd.reasons.length) && <span style={{ color:"#a5561d" }}>{nd.reasons.join("; ")}</span>}
                                </div>
                                <div style={{ fontSize:10.5, color:"#60735f", marginTop:4, fontStyle:"italic" }}>
                                  A measured guide to the trade-off, not a substitute for agent judgment.
                                </div>
                              </details>
                            </div>
                          );
                        })()}
                      </>)}
                    </div>
                  );
                })}
                <div style={{ fontSize:10.5, color:"#677167", marginTop:10, lineHeight:1.5 }}>
                  Premiums are <b>producer (net-of-subsidy), per acre</b>, penny-exact vs the R&amp;H/NAU 2027 Coverage Analyzers. The recommendation weighs best coverage for value (2027 subsidy structure + your yield risk). ARC/PLC comparison plugs in next.
                </div>
              </div>
            );
          })()}

          <div style={{ fontSize:11, color:"#677167", marginTop:6 }}>
            APH + coverage from crop_coverage; CV + yield history from production_history (≥3 seasons for CV, acre-weighted across units). Basis = APH ÷ county RMA expected.
          </div>
        </>
      )}
      </>)}
    </div>
  );
}

// ── SCO / ECO COVERAGE PERCENTAGE — the one control ─────────────────────────
// RMA lets a grower insure 50–100% of the SCO band and, separately, of the ECO
// band (default 100%); coverage and premium both scale with it. See the sourcing
// on areaCoveragePct in src/scoEcoBands.js. Every surface that quotes SCO or ECO
// renders THIS control, so the range, the step and the label cannot differ
// between the blank quote, a farm's card and the backtest.
//
// The step is 5 because that is the grain every election in the book was made at
// (50 / 75 / 80 / 90 / 100); RMA publishes the range and no increment, so a stored
// value off that grain is SHOWN exactly rather than snapped to a neighbour.
// `label` is printed beside the slider; `name` is only its accessible name, for a
// slider that sits next to a checkbox already saying "SCO".
function AreaCovPctSlider({ label = null, name = null, pct, on = true, onChange, compact = false }) {
  const p = areaCoveragePct(pct);
  const shown = p == null ? AREA_COVERAGE_PCT_DEFAULT : p;
  return (
    <span title="RMA lets a grower elect 50–100% of the SCO or ECO band (default 100%). Coverage and premium both scale with it."
          style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize: compact ? 11.5 : 12, color: on ? "#5c6f5c" : "#c1ccc1" }}>
      {label && <span style={{ fontWeight:700 }}>{label}</span>}
      <input type="range" min={AREA_COVERAGE_PCT_MIN} max={AREA_COVERAGE_PCT_MAX} step={5} value={shown} disabled={!on}
             aria-label={`${name || label || "Area endorsement"} coverage percentage`}
             onChange={e => onChange(+e.target.value)}
             style={{ width: compact ? 70 : 84, accentColor:"#2d6a2d", cursor: on ? "pointer" : "default" }} />
      <b style={{ minWidth:36, textAlign:"right", color: on ? "#1f5c2e" : "#c1ccc1" }}>{p == null ? "—" : `${Number.isInteger(shown) ? shown : shown.toFixed(2)}%`}</b>
    </span>
  );
}

// The coverage percentage a grower HOLDS on the SCO or ECO that sits on one base
// coverage: same crop, county and practice, and the SAME crop year — a 2027 ECO
// row must not answer for a 2026 base. `test` is the caller's plan predicate
// (isScoPlan / isEcoPlan); `yearOf` its crop-year lookup. A grower carrying no
// such endorsement has made no election, which is RMA's 100% default; so is a
// stored value RMA does not sell, because this only SEEDS a control the reader
// can see and move — it never prices a document on its own.
function heldAreaCoveragePct(endorsements, base, test, yearOf) {
  if (!base) return AREA_COVERAGE_PCT_DEFAULT;
  const c = (base.county || "").toUpperCase(), prac = practiceCodeOf(base), y = yearOf(base);
  if (y == null) return AREA_COVERAGE_PCT_DEFAULT;
  // TYPE too, where both rows state one: corn grain (016) and silage (026) are separate
  // products with separate endorsements, and one type's election must not seed the other.
  const bt = String(base.type_code == null ? "" : base.type_code).trim();
  const hit = (endorsements || []).find(e => (e.crop || "") === (base.crop || "")
    && (e.county || "").toUpperCase() === c && practiceCodeOf(e) === prac
    && (!bt || !String(e.type_code == null ? "" : e.type_code).trim() || String(e.type_code).trim() === bt)
    && test(e.plan) && yearOf(e) === y);
  const p = hit ? endorsementCoveragePct(hit) : null;
  return p == null ? AREA_COVERAGE_PCT_DEFAULT : p;
}

// ── Historical SCO/ECO trigger backtest ──────────────────────────────────────
// "If you'd owned TODAY's ECO/SCO every year, what would it have paid?" Walks
// every year we have county revenue data for (sco_eco_county) and recomputes the
// SCO (level→90) + ECO (90→95) payout on the current-product basis via
// backtestScoEco(). APH is held constant; each year uses THAT year's RMA county
// yield + projected price. Data-limited to whatever years are backfilled — the
// table auto-fills as historical rows land.
//
// `scoCovPct` / `ecoCovPct` are the coverage percentages the dollars are figured
// at. With `covPctControl` the card offers its own sliders, SEEDED from those props
// (the grower's own election, where the caller knows it) and re-seeded whenever
// the caller's value changes. Without it the props are used as given — the blank
// quote drives them from its own sliders and passes 0 for an unticked layer.
function ScoEcoBacktest({ stateFips, county, crop, aph, baseCl = 0.75, scoCovPct = 100, ecoCovPct = 100, acres = null, label = null, bands = null, onBands = null, covPctControl = false }) {
  const [scoPctSel, setScoPctSel] = useState(scoCovPct);
  const [ecoPctSel, setEcoPctSel] = useState(ecoCovPct);
  useEffect(() => { setScoPctSel(scoCovPct); }, [scoCovPct]);
  useEffect(() => { setEcoPctSel(ecoCovPct); }, [ecoCovPct]);
  const scoPctUse = covPctControl ? scoPctSel : scoCovPct;
  const ecoPctUse = covPctControl ? ecoPctSel : ecoCovPct;
  const st = FIPS_STATE[stateFips] || stateFips;
  // WHICH BANDS THE TABLE IS SHOWING. SCO and ECO read the SAME county revenue
  // ratio but start paying at DIFFERENT thresholds — ECO from 95%, SCO from 90% —
  // so a stacked view can't answer "which one actually pays in a year like this".
  // Uncontrolled by default; a parent rendering one card per county passes
  // { bands, onBands } so a three-county farm toggles once, not three times.
  const [ownBands, setOwnBands] = useState({ sco: true, eco: true });
  const B = bands || ownBands;
  const setB = onBands || setOwnBands;
  const showSco = !!B.sco, showEco = !!B.eco;
  const [rows, setRows] = useState(null);   // null = loading, [] = none
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!st || !county || !crop) { setRows([]); return; }
    setRows(null); setErr(false);
    (async () => {
      try {
        const r = await sbFetch("sco_eco_county", {
          filters: [`state=eq.${st}`, `county=eq.${encodeURIComponent(county)}`, `crop=eq.${encodeURIComponent(crop)}`],
          select: "crop_year,revenue_ratio,expected_yield,rma_final_yield,projected_price,harvest_price",
        });
        if (!alive) return;
        setRows((r || []).filter(x => x.revenue_ratio != null).sort((a, b) => a.crop_year - b.crop_year));
      } catch { if (alive) { setErr(true); setRows([]); } }
    })();
    return () => { alive = false; };
  }, [st, county, crop]);

  const baseClPct = Math.round(baseCl * 100);
  const A = +aph || null;
  const AC = +acres || null;                 // farmer's acres → total-$ column when present
  const years = useMemo(() => (rows || []).map(r => ({
    ...r,
    ratio: +r.revenue_ratio,
    bt: backtestScoEco({ ratioPct: +r.revenue_ratio, baseClPct, aph: A, projPrice: +r.projected_price || null, scoCovPct: scoPctUse, ecoCovPct: ecoPctUse }),
  })), [rows, baseClPct, A, scoPctUse, ecoPctUse]);

  const wrap = { border: "1px solid #d5e2d5", borderRadius: 10, background: "#fbfdfb", padding: "12px 14px", marginTop: 16 };
  const head = { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: "#60735f", fontWeight: 700, marginBottom: 9 };
  if (rows === null) return <div style={wrap}><div style={head}>Historical trigger backtest</div><div style={{ fontSize: 12, color: "#677167" }}>Loading {st} {county} {crop}…</div></div>;
  if (err) return <div style={wrap}><div style={head}>Historical trigger backtest</div><div style={{ fontSize: 12, color: "#a5461b" }}>Couldn't load county revenue history.</div></div>;
  if (!years.length) return (
    <div style={wrap}>
      <div style={head}>Historical trigger backtest</div>
      {/* Says what is actually true. This used to promise the table "fills in
          automatically as they land" — there is no cron behind it: settled years
          arrive only when tools/rma-rirs/pull.mjs is run against RMA's report. */}
      <div style={{ fontSize: 12, color: "#677167" }}>No county revenue history loaded for {st} {county} {crop}. RMA publishes settled county revenue from <b>2017</b> forward; this county/crop hasn't been pulled yet.</div>
    </div>
  );

  const scoYears = years.filter(y => y.bt && y.bt.scoFactor > 0).length;
  const ecoYears = years.filter(y => y.bt && y.bt.ecoFactor > 0).length;
  const trigYears = years.filter(y => y.bt && ((showSco && y.bt.scoFactor > 0) || (showEco && y.bt.ecoFactor > 0)));
  const hasDollars = A != null;
  // Acres no longer drive any figure — every dollar here is per-acre. This only decides
  // whether the header can label the unit's size alongside the year range.
  const hasAcres = hasDollars && AC != null;
  const indemAc = y => (showSco ? (y.bt.scoIndemAc || 0) : 0) + (showEco ? (y.bt.ecoIndemAc || 0) : 0);
  // The FIRST county revenue ratio that pays anything under the current selection.
  // ECO starts at 95, SCO at 90 — so the old hardcoded "trigger <90%" column header
  // was wrong whenever ECO was in view (Ontario 2024 sat at 92% and paid $13 of ECO
  // under a header that said nothing triggers above 90).
  const trigTop = showEco ? 95 : showSco ? 90 : null;
  // Bottom of the lowest selected band = the ratio at which it is paying its full
  // limit. Below that the coverage is maxed and the loss is the farm policy's.
  const bandBottom = showSco ? baseClPct : showEco ? 90 : null;
  const avgTrigAc = hasDollars && trigYears.length ? trigYears.reduce((s, y) => s + indemAc(y), 0) / trigYears.length : null;
  // avg over ALL years = expected annual value of the coverage over the sample
  const avgAllAc = hasDollars && years.length ? years.reduce((s, y) => s + indemAc(y), 0) / years.length : null;
  const biggest = hasDollars ? years.reduce((m, y) => indemAc(y) > (m ? indemAc(m) : 0) ? y : m, null) : null;
  // Sub-dollar payouts are real triggers; rounding them rendered "$0", which reads as
  // a broken cell rather than "it barely paid" (Monroe 2024 wheat: a 89.96% ratio pays
  // $0.19/ac of SCO). The printed page follows the same rule.
  const dac = n => n == null ? "—" : (n > 0 && n < 0.5) ? "<$1" : "$" + Math.round(n).toLocaleString();
  const N = years.length;
  // Everything is per-acre, so the layout only changes with the band selection.
  const nBand = (showSco ? 1 : 0) + (showEco ? 1 : 0);
  const cols = nBand === 0 ? "44px 1fr" : !hasDollars ? "44px 1fr 90px" : "44px 1fr" + " 74px".repeat(nBand);
  const cbx = on => ({ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 800,
                       cursor: "pointer", userSelect: "none", color: on ? "#1f5c2e" : "#677167" });

  return (
    <div style={wrap}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        <div style={head}>{label ? label + " · " : ""}Historical trigger backtest · {btBandLabel({ sco: showSco, eco: showEco, baseClPct })}</div>
        <div style={{ fontSize: 11, color: "#60735f" }}>{years[0].crop_year}–{years[N - 1].crop_year} · {N} yr{N === 1 ? "" : "s"}{hasAcres ? ` · ${AC.toFixed(0)} ac` : ""}</div>
      </div>
      {/* Band picker. Two checkboxes, both on by default — SCO alone, ECO alone, or
          stacked. "both" is a one-click way back from either single view. */}
      <div style={{ display: "flex", gap: 13, alignItems: "center", flexWrap: "wrap", marginBottom: 9 }}>
        <span style={{ fontSize: 10, color: "#677167", fontWeight: 700, letterSpacing: ".04em" }}>SHOW</span>
        <label style={cbx(showSco)}>
          <input type="checkbox" checked={showSco} onChange={() => setB({ ...B, sco: !showSco })} style={{ accentColor: "#2d6a2d", width: 13, height: 13, cursor: "pointer" }} />
          SCO <span style={{ fontWeight: 600, color: "#677167" }}>{baseClPct}→90</span>
        </label>
        <label style={cbx(showEco)}>
          <input type="checkbox" checked={showEco} onChange={() => setB({ ...B, eco: !showEco })} style={{ accentColor: "#2d6a2d", width: 13, height: 13, cursor: "pointer" }} />
          ECO <span style={{ fontWeight: 600, color: "#677167" }}>90→95</span>
        </label>
        {!(showSco && showEco) && (
          <button onClick={() => setB({ sco: true, eco: true })}
            style={{ fontSize: 11, fontWeight: 700, color: "#2d6a2d", background: "#fff", border: "1px solid #cfe3cf", borderRadius: 6, padding: "2px 9px", cursor: "pointer" }}>both</button>
        )}
        {/* The dollars are figured on this share of each band. Only offered where
            there ARE dollars — without an APH the card shows payout factors, and a
            factor does not depend on how much of the band was bought. */}
        {covPctControl && hasDollars && (
          <>
            <AreaCovPctSlider label="SCO" pct={scoPctSel} on={showSco} onChange={setScoPctSel} compact />
            <AreaCovPctSlider label="ECO" pct={ecoPctSel} on={showEco} onChange={setEcoPctSel} compact />
          </>
        )}
      </div>
      {/* summary line */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "baseline", marginBottom: 10 }}>
        {showSco && <div style={{ fontSize: 13 }}><b style={{ color: "#1f5c2e", fontSize: 16 }}>{scoYears}</b><span style={{ color: "#60735f" }}> / {N} yrs SCO paid</span></div>}
        {showEco && <div style={{ fontSize: 13 }}><b style={{ color: "#1f5c2e", fontSize: 16 }}>{ecoYears}</b><span style={{ color: "#60735f" }}> / {N} yrs ECO paid</span></div>}
        {showSco && showEco && <div style={{ fontSize: 13 }}><b style={{ color: "#1f5c2e", fontSize: 16 }}>{trigYears.length}</b><span style={{ color: "#60735f" }}> / {N} yrs either paid</span></div>}
        {nBand === 0 && <div style={{ fontSize: 12, color: "#677167" }}>No band selected — the bars below are the county's revenue history on its own.</div>}
        {nBand > 0 && hasDollars && <div style={{ fontSize: 13 }}><span style={{ color: "#60735f" }}>avg in trigger yrs </span><b style={{ color: "#1f5c2e", fontSize: 16 }}>{dac(avgTrigAc)}</b><span style={{ color: "#60735f" }}>/ac</span></div>}
        {nBand > 0 && hasDollars && avgAllAc != null && <div style={{ fontSize: 13 }}><span style={{ color: "#60735f" }}>avg all yrs </span><b style={{ color: "#1f5c2e", fontSize: 16 }}>{dac(avgAllAc)}</b><span style={{ color: "#60735f" }}>/ac</span></div>}
        {nBand > 0 && hasDollars && biggest && indemAc(biggest) > 0 && <div style={{ fontSize: 13 }}><span style={{ color: "#60735f" }}>worst yr ({biggest.crop_year}) </span><b style={{ color: "#b8500a", fontSize: 16 }}>{dac(indemAc(biggest))}</b><span style={{ color: "#60735f" }}>/ac</span></div>}
      </div>
      {/* per-year rows */}
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: "5px 10px", alignItems: "center", fontSize: 12 }}>
        <div style={{ fontSize: 10, color: "#677167", fontWeight: 700 }}>YR</div>
        <div style={{ fontSize: 10, color: "#677167", fontWeight: 700 }}>COUNTY REV vs EXPECTED{trigTop != null ? ` (trigger <${trigTop}%)` : ""}</div>
        {nBand === 0 ? null : hasDollars
          ? <>
              {showSco && <div style={{ fontSize: 10, color: "#677167", fontWeight: 700, textAlign: "right" }}>SCO $/AC</div>}
              {showEco && <div style={{ fontSize: 10, color: "#677167", fontWeight: 700, textAlign: "right" }}>ECO $/AC</div>}
            </>
          : <div style={{ fontSize: 10, color: "#677167", fontWeight: 700, textAlign: "right" }}>PAYOUT FACTOR</div>}
        {/* Newest year on top. Copy before reversing — `years` stays ascending because the
            range label reads years[0]..years[N-1], and .reverse() mutates in place. */}
        {[...years].reverse().map(y => {
          const paid = y.bt && ((showSco && y.bt.scoFactor > 0) || (showEco && y.bt.ecoFactor > 0));
          const r = y.ratio;                                  // county rev ratio %
          const barPct = Math.max(0, Math.min(100, r));       // 0–100 fill
          // Tick marks are the SELECTED bands' edges — the top one (where the first
          // dollar lands) drawn solid, the rest faint. Without this the 90 line stayed
          // put while the numbers beside it moved to a 95 trigger.
          const marks = [];
          if (showEco) marks.push({ at: 95, top: true, t: "95% — ECO top (first dollar)" }, { at: 90, top: false, t: "90% — ECO fully paid / SCO top" });
          if (showSco) { if (!showEco) marks.push({ at: 90, top: true, t: "90% — SCO top (first dollar)" }); marks.push({ at: baseClPct, top: false, t: `${baseClPct}% — SCO fully paid (your MPCI level)` }); }
          return (
            <React.Fragment key={y.crop_year}>
              <div style={{ fontWeight: 700, color: paid ? "#1f5c2e" : "#6a7a6a" }}>{y.crop_year}</div>
              <div style={{ position: "relative", height: 18, background: "#eef3ee", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: barPct + "%", background: bandBottom == null ? "#8aa08a" : r < bandBottom ? "#c0392b" : r < trigTop ? "#e08a1e" : "#3a8a4a", opacity: 0.85 }} />
                {marks.map(m => <div key={m.at} style={{ position: "absolute", left: m.at + "%", top: 0, bottom: 0, width: 1.5, background: m.top ? "#33553a" : "#677167" }} title={m.t} />)}
                <div style={{ position: "absolute", left: 4, top: 0, bottom: 0, display: "flex", alignItems: "center", fontSize: 10.5, fontWeight: 700, color: "#22331f" }}>{r.toFixed(0)}%</div>
              </div>
              {nBand === 0 ? null : hasDollars
                ? <>
                    {showSco && <div style={{ textAlign: "right", fontWeight: y.bt.scoIndemAc > 0 ? 700 : 400, color: y.bt.scoIndemAc > 0 ? "#1f5c2e" : "#b9c4b9" }}>{y.bt.scoIndemAc > 0 ? dac(y.bt.scoIndemAc) : "—"}</div>}
                    {showEco && <div style={{ textAlign: "right", fontWeight: y.bt.ecoIndemAc > 0 ? 700 : 400, color: y.bt.ecoIndemAc > 0 ? "#1f5c2e" : "#b9c4b9" }}>{y.bt.ecoIndemAc > 0 ? dac(y.bt.ecoIndemAc) : "—"}</div>}
                  </>
                : <div style={{ textAlign: "right", fontSize: 11, color: paid ? "#1f5c2e" : "#b9c4b9" }}>{paid
                    ? [showSco ? `SCO ${Math.round(y.bt.scoFactor * 100)}%` : null, showEco ? `ECO ${Math.round(y.bt.ecoFactor * 100)}%` : null].filter(Boolean).join(" · ")
                    : "no trigger"}</div>}
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: "#677167", marginTop: 9, lineHeight: 1.5 }}>
        Area-revenue trigger: county final revenue ÷ expected.
        {showSco ? ` SCO pays from your MPCI level (${baseClPct}%) up to 90%.` : ""}
        {showEco ? " ECO pays from 90% to 95%." : ""}
        {nBand === 0 ? " Tick a band above to see what it would have paid." : ""}
        {nBand > 0 && (hasDollars ? ` Dollars are PER ACRE — they hold this unit's APH (${A} bu) constant and use each year's RMA county yield + projected price, i.e. what today's coverage would have paid per acre.` : " Enter an APH for dollar estimates.")}
        {nBand > 0 && hasDollars && ((showSco && scoPctUse < 100) || (showEco && ecoPctUse < 100))
          ? ` Reduced election applied${showSco ? ` · SCO ${scoPctUse}%` : ""}${showEco ? ` · ECO ${ecoPctUse}%` : ""} — each band's dollars are that share of the full band.` : ""}
        {" "}RMA's settled county revenue starts at <b>2017</b> — that report publishes no earlier year, so 2017 is the floor on every one of these tables, not a loading gap. A crop year joins it once RMA settles the county yield (winter wheat ~Feb–Mar after harvest, corn/soybeans ~mid-following-year).
      </div>
    </div>
  );
}

// ── Renewal roster ───────────────────────────────────────────────────────────
// The customer list for one crop's renewal season, with where each grower stands
// on the three documents that make up a renewal.
//
// SEPARATION OF CONCERNS (Kevin, 2026-08-04): File Management RUNS the workflow —
// it owns the AI-fill, the sends, and (next) moving files between folders. This
// list only READS. Dropbox is the bridge between the two, so there is no shared
// frontend state and no cross-page import; both pages call the same scan endpoint
// and render it their own way. Nothing here writes.
//
// The roster is COVERAGE-first, not document-first: a grower appears because they
// carry the crop, not because a document exists for them. That inversion is the
// point — the customer who most needs a call is the one with no paperwork yet, and
// a document-first list is exactly the list that hides them.
const CA_RENEWAL = {
  WHEAT:    { season: "FALL",   crops: ["WHEAT", "WINTER WHEAT"] },
  CORN:     { season: "SPRING", crops: ["CORN"] },
  SOYBEANS: { season: "SPRING", crops: ["SOYBEANS"] },
};
// Which crop year a season is currently renewing. Derived rather than pinned so a
// rollover doesn't silently strand the page on a stale year: fall sells about a
// year ahead (the 2027 FALL folders opened in June 2026), spring rolls after
// harvest. Overridable in the UI — a derived default should never be a cage.
function caRenewalYear(season, now = new Date()) {
  const y = now.getFullYear(), m = now.getMonth();      // 0-based
  return season === "FALL" ? (m >= 5 ? y + 1 : y) : (m >= 9 ? y + 1 : y);
}
const RENEWAL_STEPS = [
  { key: "application", label: "App / Renewal" },
  { key: "production",  label: "Production" },
  { key: "acreage",     label: "Acreage" },
];
// Stage → how it reads to someone deciding whether to pick up the phone.
// "done" is ONLY keyed: e-sign moved signing ahead of keying, so a signed report is
// still outstanding work until it's in the AIP. Mirrors STAGE_RANK in api/_dropbox.js.
//
// `lbl` is the DROPBOX FOLDER NAME, not a paraphrase of it. A box that says
// "signed, needs keying" when the folder is called SIGNED & PENDING makes you
// translate before you can act; naming the folder means the box and the place
// you go to work it are the same word. `blank` is the one stage with no folder —
// it is a PDF loose in the doc root, so it is described as exactly that rather
// than assigned a meaning the file position doesn't carry.
const RENEWAL_STAGE = {
  blank:           { tone: "todo", lbl: "In folder · unsorted",  txt: "loose in the doc root — not filed into a stage folder yet" },
  need_signed:     { tone: "wait", lbl: "Need signed",           txt: "pre-keyed · out with the farmer for signature" },
  pre_prints:      { tone: "wait", lbl: "Pre-prints reviewed",   txt: "prepared · with farmer" },
  signed_pending:  { tone: "work", lbl: "Signed & Pending",      txt: "signed · not yet keyed" },
  signed_ready:    { tone: "work", lbl: "Signed & Ready",        txt: "signed · ready to key" },
  ready_for_silas: { tone: "work", lbl: "Ready for Silas",       txt: "signed · staged for AI fill" },
  silas_filled:    { tone: "work", lbl: "Silas filled",          txt: "filled · needs keying" },
  keyed:           { tone: "done", lbl: "Keyed",                 txt: "done · keyed" },
  // The second ending. Reviewed, nothing to change, so nothing to sign or key —
  // counted as DONE (Kevin, 2026-08-07). It has to count: on a book where half the
  // renewals need no changes, leaving them out caps the bar below 100% forever and
  // the number stops meaning "how much is left to do". Its own box keeps the split
  // from Keyed visible, so counting it can never hide it.
  no_changes:      { tone: "done", lbl: "No changes",            txt: "reviewed · nothing to change, nothing to key" },
};
// Both endings. Anything not in here is outstanding work, whoever's move it is.
const RENEWAL_DONE = new Set(["keyed", "no_changes"]);
// The stage a farmer is in when the scan found NOTHING for them. Not a Dropbox
// folder — the absence of one — but it is the most actionable box on the page,
// so it gets a key and behaves like the rest.
const RENEWAL_NONE = "__none";
// A stage the scan could NOT establish: a Dropbox folder that might hold this
// farmer's document failed to list. Never shown as a lower stage (2026-10-18).
const RENEWAL_UNKNOWN = "__unknown";
// stage-scan returns `listing_errors: [{folder, stage, error}]` for every folder
// it could not read (stage "*" = the root itself). This is the rank of the most
// advanced stage the scan was blind to; null = every folder listed. A farmer whose
// best stage ranks BELOW it may really be further along.
function stageScanBlindRank(scan, rankMap) {
  const errs = scan && Array.isArray(scan.listing_errors) ? scan.listing_errors : [];
  let blind = null;
  for (const e of errs) {
    // "*" and a stage this board cannot rank are both treated as "could be anything".
    const v = e && e.stage !== "*" && e.stage in rankMap ? rankMap[e.stage] : Infinity;
    if (blind === null || v > blind) blind = v;
  }
  return blind;
}
function stageIsBlind(stage, blindRank, rankMap) {
  if (blindRank == null) return false;
  if (!stage || !(stage in rankMap)) return true;
  return rankMap[stage] < blindRank;
}
// ── ONE AGENT MODEL, SHARED WITH PREMIUM ANALYTICS ──────────────────────────
// Kevin, 2026-09-04: "All policies fall into one of these groups/agents. Use
// this model for agents in coverage advisor." That model is AGENCY_AGENTS (the
// six real buckets, MM KK Service Account and Hylant included) plus
// PA_UNATTRIBUTED — the same seven facets the Premium Analytics agent filter
// renders, resolved through the same canonAgent().
//
// An earlier cut of this fix invented THREE Coverage-Advisor-only facets
// ("Other / external", "Not in carrier file", "Not captured"). They described
// something true, and they were still wrong: two screens answering "whose
// policy is this" with different vocabularies is how one of them comes to
// disagree with the other, which is the defect this repo keeps recording.
//
// WHY THE ABSENCE IS NOT ITS OWN FACET ANY MORE. The distinction survives — it
// just is not a bucket. policyAgentProvenance() answers "who says so" and the
// column marks it; the facet answers "whose renewal is this", which is what a
// call list is for.
const RA_UNATTRIBUTED = PA_UNATTRIBUTED;
// What each provenance means in the agent column’s own tooltip. "unsupplied"
// and "uncaptured" both render as Unattributed and are NOT the same fact: one
// is the carrier saying nothing, the other is us never having typed it.
const RA_PROV_NOTE = {
  stated: "named by the carrier on this crop year",
  override: "set by hand as the agent of record",
  carried: "carried forward from an earlier crop year of the same policy — the carrier has not restated it for this year",
  unsupplied: "the carrier names no agent on any year of this policy",
  uncaptured: "hand-loaded policy, no agent typed in and no carrier file to consult — a gap to close",
};
// Short forms for the pills and the column. "MM"/"Mock" can't both be initials —
// Meisenzahl and Mock collide — so these are surnames, which never do.
const RA_AGENT_ORDER = [...AGENCY_AGENTS, RA_UNATTRIBUTED];
const RA_AGENT_SHORT = {
  "Mike Meisenzahl": "Meisenzahl", "Kevin Keenan": "Keenan", "Michael Mock": "Mock",
  "Daniele Harris": "Harris", [MM_KK_SERVICE]: "MM KK", [HYLANT]: "Hylant",
  [RA_UNATTRIBUTED]: "Unattributed",
};

// ── WHOSE POLICY IS IT — one rule, every book-wide list ──────────────────────
// A manual `agent_name_override` is a deliberate AOR statement and must restate
// the SAME policy's other crop years too. It is matched on the policy STEM (crop
// year stripped) because NAU embeds the year in the number — NY-944-1234567-25
// and -26 are one policy under two strings — so a farm-level restatement
// over-assigns (14 of 16 affected farms are mixed) and a number-level one misses
// every prior year. Build the map once with buildAgentOverrideStems() and pass it
// in; dropping it silently leaves last year under the old agent.
// THE LAST AGENT THE CARRIER STATED FOR **THIS POLICY**, from an earlier crop
// year of the same stem.
//
// buildAgentOverrideStems below already inherits an OVERRIDE along the stem, on
// the stated grounds that "NY-944-1234567-25 and -26 are one policy under two
// strings". That is equally true of the agent the CARRIER wrote, and the stored
// name was not inherited — so a renewal read as having nobody on it while the
// farmer card showed the agent from the same policy's prior year. HARTWAY FARMS
// is the case Kevin put on screen:
//
//     NY-944-6080333-26   KEVIN KEENAN
//     NY-944-6080333-27   (null)        <- the same policy, next year
//
// MEASURED 2026-09-04 over the 150 CY2027 policies with no stored agent: 132
// (97 R&H + 35 NAU) carry one on an EARLIER year of their own stem; 18 do not.
// The 21 CY2026 cases resolve from no prior year at all, so the rule
// discriminates rather than blanketing.
//
// IT IS CARRIED, NOT ASSERTED. An AOR can change at renewal and the carrier has
// not yet said either way — a renewal's coverage record is not written until it
// is sold. So this answers the useful question (whose renewal is this) while
// policyAgentProvenance keeps the honest one (who says so) separate, and the
// column marks it. Strictly EARLIER years: this is the last STATED AOR, not a
// value assembled from whichever year is handiest.
function buildAgentCarryStems(pols) {
  // Latest year wins, so a stem whose agent changed carries the most recent
  // statement rather than the oldest.
  const m = new Map();
  [...(pols || [])]
    .filter(p => p && p.agent_name && String(p.agent_name).trim() && paPolicyStem(p.policy_number))
    .sort((a, b) => paNum(a.crop_year) - paNum(b.crop_year))
    .forEach(p => m.set(paPolicyStem(p.policy_number), { name: p.agent_name, year: paNum(p.crop_year) }));
  return m;
}
function buildAgentOverrideStems(pols) {
  // Latest crop year wins if a stem carries more than one override.
  const m = new Map();
  [...(pols || [])]
    .filter(p => p && p.agent_name_override && paPolicyStem(p.policy_number))
    .sort((a, b) => (+a.crop_year || 0) - (+b.crop_year || 0))
    .forEach(p => m.set(paPolicyStem(p.policy_number), p.agent_name_override));
  return m;
}
// WHOSE POLICY IS IT — four sources, in the order they outrank each other, and
// anything that resolves to nobody is PA_UNATTRIBUTED. canonAgent returns null
// for a name outside the roster (a prior or external agent), which lands there
// too — the same answer Premium Analytics gives.
//
// `carryByStem` is OPTIONAL and the arity is the contract: called without it, a
// renewal whose carrier record has not been written yet falls through to
// Unattributed and reads as having nobody on it. Build it once with
// buildAgentCarryStems() and pass it in — the same rule buildAgentOverrideStems
// already follows.
function policyAgentOf(policy, ovrByStem, carryByStem) {
  const p = policy || {};
  const own = p.agent_name_override || (ovrByStem && ovrByStem.get(paPolicyStem(p.policy_number)));
  if (own) return canonAgent(own) || RA_UNATTRIBUTED;
  if (p.agent_name) return canonAgent(p.agent_name) || RA_UNATTRIBUTED;
  const carried = carriedAgentFor(p, carryByStem);
  if (carried) return canonAgent(carried.name) || RA_UNATTRIBUTED;
  return RA_UNATTRIBUTED;
}
// The carry lookup, defined once so the resolver and the provenance cannot
// disagree about which year they took it from.
function carriedAgentFor(policy, carryByStem) {
  const p = policy || {};
  if (!carryByStem) return null;
  const hit = carryByStem.get(paPolicyStem(p.policy_number));
  if (!hit || !hit.name) return null;
  const yr = paNum(p.crop_year);
  // EARLIER YEARS ONLY. A policy is not evidence about itself, and a later year
  // is not evidence about an earlier one.
  return (yr && hit.year && hit.year < yr) ? hit : null;
}
// WHO SAYS SO — deliberately apart from WHICH AGENT. The facet has to return a
// NAME so a renewal lands in that agent's call list; this is how the screen can
// still say the name was carried rather than stated, and tell the carrier's
// silence apart from our own.
//   stated      the carrier named this agent on THIS crop year
//   override    a human restated the AOR (wins over everything)
//   carried     the last agent the carrier stated for this policy, earlier year
//   unsupplied  a sync-owned policy with no agent anywhere on the stem
//   uncaptured  a hand-loaded policy: no file to consult, and a real gap
function policyAgentProvenance(policy, ovrByStem, carryByStem) {
  const p = policy || {};
  if (p.agent_name_override || (ovrByStem && ovrByStem.get(paPolicyStem(p.policy_number)))) return "override";
  if (p.agent_name) return "stated";
  if (carriedAgentFor(p, carryByStem)) return "carried";
  return isSyncFormatPolicyNumber(p.policy_number) ? "unsupplied" : "uncaptured";
}
// States the 2027 wheat rate sets actually cover (WHEAT_STATES, keyed by FIPS).
// Anything outside this is NOT quotable: stFips() defaults an unknown state to "36",
// so an out-of-footprint policy would silently price off New York's rates.
const CA_QUOTE_STATES = new Set(Object.keys(WHEAT_STATES || {}).map(f => FIPS_STATE[f]).filter(Boolean));
// Small pill used by the sort / county / state filter buttons.
const sortBtn = (on) => ({
  fontSize: 11.5, fontWeight: 700, padding: "5px 11px", borderRadius: 999, cursor: "pointer",
  border: "1px solid " + (on ? "#2d4a2d" : "#d5e2d5"), background: on ? "#2d4a2d" : "#fff", color: on ? "#fff" : "#5c6f5c",
});
// Explicit ladder order — a farmer holding several policies takes the most advanced
// stage found. Mirrors STAGE_RANK in api/_dropbox.js; keep the two in step.
const RENEWAL_RANK = { blank: 0, need_signed: 1, pre_prints: 1, signed_pending: 2, signed_ready: 3, ready_for_silas: 4, silas_filled: 5, no_changes: 6, keyed: 6 };
// Progress-bar segments, most→least advanced. Only `done` is a solid fill: the
// rest are tints so a bar that is merely BUSY can never be mistaken for a bar
// that is finished. Wording matches the chip legend at the foot of the page.
const RENEWAL_BAR_ORDER = ["done", "work", "wait", "todo"];
const RENEWAL_BAR = {
  done: { fill: "#2d6a2d", lbl: "keyed / no changes" },
  work: { fill: "#e8c274", lbl: "signed · not keyed" },
  wait: { fill: "#cddcea", lbl: "with the farmer" },
  todo: { fill: "#e0d8c6", lbl: "in folder · unsorted" },
};
const RENEWAL_TONE = {
  done: { bg: "#e7f3e7", fg: "#1f7a3d", bd: "#b7d3b7", mark: "✓" },
  work: { bg: "#fdf4e3", fg: "#8a6100", bd: "#e8d5a8", mark: "◐" },
  wait: { bg: "#eef3f8", fg: "#3a5570", bd: "#cddcea", mark: "◔" },
  todo: { bg: "#f6f3ec", fg: "#7a6a4a", bd: "#e0d8c6", mark: "•" },
  none: { bg: "#f4f6f4", fg: "#677167", bd: "#e7eee7", mark: "—" },
};

// Shared pill styling for the roster cells — one definition so the PRF list and
// the crop lists cannot drift into looking like different products. Declared
// ABOVE its only consumer: a module-level const used by a function defined
// earlier is a temporal-dead-zone hazard the moment anything calls that
// function during module evaluation.
const cellPill = (t) => ({
  display: "inline-block", fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 6,
  background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, whiteSpace: "nowrap",
});

// ── PRF renewals ─────────────────────────────────────────────────────────────
// The fourth renewal list, and the only one whose renewal year is usually EMPTY
// when you open it. Everything about why it is built on the base year instead
// lives in src/prf/prfRenewals.js — this component only fetches and draws.
//
// It sits beside the crop lists rather than inside the PRF workspace because it
// answers the renewal question, not the quoting one: "who carries PRF and where
// does each of them stand". The PRF workspace (New Quote / Customers / Grid
// Explorer / Book Review) is for pricing one farm at a time.
//
// AGENT-ONLY. It reads the whole PRF book, which is the one thing a Markets-style
// grant may never do — the PRF Grid leak is the precedent (see CLAUDE.md), and
// this component is never mounted from a portal root.
function PRFRenewalRoster({ isMobile = false }) {
  const [pols, setPols] = useState([]);
  const [prfPols, setPrfPols] = useState([]);
  const [ivs, setIvs] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [scan, setScan] = useState(null);       // { docs, prf_roots } | null
  const [scanErr, setScanErr] = useState(null);
  const [scanning, setScanning] = useState(true);
  const [year, setYear] = useState(null);       // null until the book says
  const [yearTouched, setYearTouched] = useState(false);
  const [only, setOnly] = useState(null);       // null | "torenew" | "renewed" | "nostart"
  const [agPick, setAgPick] = useState(() => new Set());
  const [stPick, setStPick] = useState(() => new Set());
  const [ctyPick, setCtyPick] = useState(() => new Set());
  const [sort, setSort] = useState({ col: "name", dir: "asc" });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const [p, pp, iv, f] = await Promise.all([
          sbFetch("policies", { select: "id,farmer_id,policy_number,crop_year,agent_name,agent_name_override,state,aip,policy_type", filters: ["policy_type=eq.prf"] }),
          sbFetch("prf_policies", { select: "id,policy_id,policy_number,county,coverage_level,total_acres,total_liability,total_premium" }),
          sbFetch("prf_intervals", { select: "prf_policy_id,county,grid_id,interval_code,insured_acres" }),
          sbFetch("farmers", { select: "id,name" }),
        ]);
        if (!alive) return;
        const nm = {}; (f || []).forEach(r => { if (r.id) nm[r.id] = r.name; });
        setPols(p || []); setPrfPols(pp || []); setIvs(iv || []); setNames(nm);
      } catch (e) { if (alive) setErr(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);


  // The PRF folder scan, after the roster renders so the list is never blocked
  // on Dropbox. summary=1 — this page wants the rollup, not the folder dump.
  useEffect(() => {
    if (!year) return;
    let alive = true;
    (async () => {
      setScanning(true); setScanErr(null);
      try {
        const r = await fetch(`/api/market?fn=dropbox&action=stage-scan&season=PRF&year=${year}&summary=1`,
          { method: "POST", headers: getApiAuthHeaders() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        // ⚠ A 200 IS NOT AN ANSWER. A response that parses but carries no `docs`
        // — a proxy, a rewrite, an endpoint that moved — would leave every row
        // reading "nothing filed" with no absence notice, which is the
        // no-op-that-returns-200 failure this repo already records. Refuse the
        // shape rather than render it as a fact about the customers.
        if (!d || !d.docs) throw new Error("the scan returned no document data");
        if (alive) setScan(d);
      } catch (e) { if (alive) { setScanErr(e.message); setScan(null); } }
      finally { if (alive) setScanning(false); }
    })();
    return () => { alive = false; };
  }, [year]);

  // One shape for the engine: the PRF policy rows, each carrying the crop year,
  // carrier and state off its parent `policies` row.
  const enginePolicies = useMemo(() => {
    const polById = {}; (pols || []).forEach(p => { polById[p.id] = p; });
    const ivBy = {}; (ivs || []).forEach(r => { (ivBy[r.prf_policy_id] = ivBy[r.prf_policy_id] || []).push(r); });
    const out = [];
    for (const pp of prfPols || []) {
      const parent = polById[pp.policy_id];
      if (!parent || !parent.farmer_id) continue;   // not a PRF policy we hold, or unattached
      out.push({
        farmerId: parent.farmer_id,
        cropYear: parent.crop_year,
        aip: parent.aip || null,
        state: parent.state || null,
        // The PRF row's own number where it has one, else the parent policy's —
        // the Dropbox filenames are written from whichever the AIP printed.
        policyNumber: pp.policy_number || parent.policy_number,
        // The PARENT policy's id — what `?fn=prf-lease-cert` keys on. Additive; it
        // changes no row the roster already built. The RH-5237 is per POLICY (item 4
        // is a single "Policy No"), so a farm holding two PRF policies needs two
        // sheets and the button offers one per policy rather than picking.
        policyId: parent.id || null,
        county: pp.county || null,
        coverageLevel: pp.coverage_level,
        totalAcres: pp.total_acres,
        totalLiability: pp.total_liability,
        totalPremium: pp.total_premium,
        intervals: (ivBy[pp.id] || []).map(r => ({ gridId: r.grid_id, county: r.county, intervalCode: r.interval_code, insuredAcres: r.insured_acres })),
      });
    }
    return out;
  }, [pols, prfPols, ivs]);

  // The renewal year follows the book until a human picks one.
  //
  // ⚠ IT READS `enginePolicies`, NOT THE RAW POLICY ROWS, AND THAT IS THE WHOLE FIX.
  // `policies` carries a row for every crop year the AIP has opened, including the
  // CY2027 SHELLS that PRF's continuous renewal creates before anything is sold —
  // so `max(crop_year) + 1` defaulted this page to CY2028, two crop years out.
  // `enginePolicies` carries the elected figures (acres, liability, premium,
  // intervals) that `prfRenewalYear` needs to tell a written policy from a shell.
  //
  // ⚠ IT ALSO HAS TO SIT BELOW `enginePolicies`: a useMemo's dep array is evaluated
  // where it is written, so reading it from above would be a temporal-dead-zone
  // ReferenceError at render — which is what `check-tdz` exists to catch.
  const bookYear = useMemo(
    () => PRF_RENEW.prfRenewalYear({ prfPolicies: enginePolicies }),
    [enginePolicies]);
  useEffect(() => { if (!yearTouched) setYear(bookYear); }, [bookYear, yearTouched]);

  // Whose renewal it is — the SAME rule the crop roster uses, carried across
  // crop years by policy stem. Not re-derived here.
  const agentOf = useMemo(() => {
    const ovrByStem = buildAgentOverrideStems(pols);
    const byNumber = {}; (pols || []).forEach(p => { if (p.policy_number) byNumber[p.policy_number] = p; });
    return (policyNumber, row) => {
      const p = byNumber[policyNumber] || (row && row._policy) || null;
      return p ? policyAgentOf(p, ovrByStem) : null;
    };
  }, [pols]);

  const built = useMemo(
    () => PRF_RENEW.buildPrfRenewalRows({
      prfPolicies: enginePolicies, namesById: names, year: year || bookYear,
      stages: scan && scan.docs, agentOf,
    }),
    [enginePolicies, names, year, bookYear, scan, agentOf]);
  const rows = built.rows;

  const unmatched = useMemo(
    () => PRF_RENEW.prfUnmatchedDocCores({ stages: scan && scan.docs, rows }),
    [scan, rows]);

  const allAgents = useMemo(() => {
    const s = new Set(); rows.forEach(r => r.agents.forEach(a => s.add(a)));
    return RA_AGENT_ORDER.filter(a => s.has(a));
  }, [rows]);
  const allStates = useMemo(() => {
    const s = new Set(); rows.forEach(r => r.states.forEach(x => s.add(x)));
    return [...s].sort((a, b) => (a === "NY" ? -1 : b === "NY" ? 1 : a.localeCompare(b)));
  }, [rows]);
  const allCounties = useMemo(() => {
    const s = new Set();
    rows.forEach(r => (stPick.size ? r.states.some(x => stPick.has(x)) : true) && r.counties.forEach(c => s.add(c)));
    return [...s].sort();
  }, [rows, stPick]);
  // Drop picks that don't exist after a year switch, or the list silently empties.
  useEffect(() => { setCtyPick(prev => { const k = new Set([...prev].filter(c => allCounties.includes(c))); return k.size === prev.size ? prev : k; }); }, [allCounties]);
  useEffect(() => { setAgPick(prev => { const k = new Set([...prev].filter(a => allAgents.includes(a))); return k.size === prev.size ? prev : k; }); }, [allAgents]);

  const scoped = useMemo(() => rows.filter(r =>
    (!agPick.size || r.agents.some(a => agPick.has(a))) &&
    (!stPick.size || r.states.some(s => stPick.has(s))) &&
    (!ctyPick.size || r.counties.some(c => ctyPick.has(c)))
  ), [rows, agPick, stPick, ctyPick]);

  const totals = useMemo(() => PRF_RENEW.prfRenewalTotals(scoped), [scoped]);

  const shown = useMemo(() => {
    let v = scoped;
    if (only === "torenew") v = v.filter(r => !r.renewed);
    else if (only === "renewed") v = v.filter(r => r.renewed);
    else if (only === "nostart") v = v.filter(r => !r.renewed && !r.docStage);
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (r) => {
      if (sort.col === "agent") return r.agents.length ? RA_AGENT_ORDER.indexOf(r.agents[0]) : 99;
      if (sort.col === "county") return r.counties[0] || "";
      if (sort.col === "acres") return (r.base && r.base.acres) || 0;
      if (sort.col === "progress") return (r.renewed ? 2 : 0) + (r.docDone ? 1 : 0);
      return r.name;
    };
    return [...v].sort((a, b) => { const x = val(a), y = val(b);
      return (typeof x === "string" ? String(x).localeCompare(String(y)) : x - y) * dir || a.name.localeCompare(b.name); });
  }, [scoped, only, sort]);

  const clickSort = (c) => setSort(s => s.col === c ? { col: c, dir: s.dir === "asc" ? "desc" : "asc" } : { col: c, dir: "asc" });
  const Stat = ({ k, lbl, val, sub }) => (
    <button onClick={() => setOnly(only === k ? null : k)} disabled={!k}
      style={{ textAlign: "left", padding: "9px 12px", borderRadius: 10, cursor: k ? "pointer" : "default",
               border: "1px solid " + (only === k ? "#2d4a2d" : "#d5e2d5"), background: only === k ? "#eaf3ea" : "#fff", minWidth: 104 }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{val}</div>
      <div style={{ fontSize: 10.5, color: "#5c6f5c", fontWeight: 700 }}>{lbl}</div>
      {sub ? <div style={{ fontSize: 10.5, color: "#677167" }}>{sub}</div> : null}
    </button>
  );
  const tone = (t) => RENEWAL_TONE[t] || RENEWAL_TONE.none;
  const stageCell = (st) => {
    if (!st) return <span style={{ ...cellPill(RENEWAL_TONE.none) }}>— nothing filed</span>;
    const meta = RENEWAL_STAGE[st];
    return <span title={meta ? meta.txt : st} style={{ ...cellPill(tone(meta ? meta.tone : "none")) }}>
      {(meta ? tone(meta.tone).mark : "•")} {meta ? meta.lbl : st}
    </span>;
  };
  const prfRoots = (scan && scan.prf_roots) || null;
  const docMissing = !!(scan && scan.docs && scan.docs.prf && scan.docs.prf.root_missing);
  const soiMissing = !!(scan && scan.docs && scan.docs.prf_soi && scan.docs.prf_soi.root_missing);
  const unknownFolders = (scan && scan.docs && scan.docs.prf && scan.docs.prf.unknown_folders) || [];

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "#5c6f5c", fontWeight: 700, marginBottom: 4 }}>Renewal year</div>
          <select value={year || ""} onChange={e => { setYearTouched(true); setYear(+e.target.value); }}
            style={{ fontSize: 14, padding: "7px 10px", borderRadius: 7, border: "1px solid #d5e2d5", background: "#fff" }}>
            {[bookYear - 2, bookYear - 1, bookYear, bookYear + 1].filter((v, i, a) => a.indexOf(v) === i).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "#5c6f5c", fontWeight: 700, marginBottom: 4 }}>Sort by</div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {[["name", "Farmer"], ["agent", "Agent"], ["county", "County"], ["acres", "Acres"], ["progress", "Progress"]].map(([k, lbl]) => (
              <button key={k} onClick={() => clickSort(k)} style={sortBtn(sort.col === k)}>
                {lbl}{sort.col === k ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* WHAT THIS LIST IS. Said on the page, not just in the code, because a
          roster whose rows come from a DIFFERENT year than its header is exactly
          the kind of thing that reads as a bug to the next person. */}
      <div style={{ fontSize: 12, color: "#3a4a38", background: "#f2f6f2", border: "1px solid #dde8d8", borderRadius: 9, padding: "9px 12px", marginBottom: 12, lineHeight: 1.6 }}>
        Everyone carrying PRF in <b>{built.baseYear}</b> — the book to renew for <b>{built.renewalYear}</b> — plus anyone already written for {built.renewalYear}.
        {" "}<b>Renewed</b> means the AIP file names a {built.renewalYear} PRF policy for them.
        {" "}No sales-closing date is shown: RMA publishes it per county in the actuarial documents and this database holds no such table.
      </div>

      {err && <div style={{ padding: 12, color: "#a5461b", background: "#fbeee6", border: "1px solid #eccdba", borderRadius: 9, marginBottom: 12, fontSize: 12.5 }}>Couldn’t load the PRF book: {err}</div>}

      {allAgents.length > 1 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "#5c6f5c", fontWeight: 700, marginBottom: 4 }}>Agent</div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            <button onClick={() => setAgPick(new Set())} style={sortBtn(agPick.size === 0)}>All</button>
            {allAgents.map(a => (
              <button key={a} onClick={() => setAgPick(p => { const n = new Set(p); if (n.has(a)) n.delete(a); else n.add(a); return n; })} style={sortBtn(agPick.has(a))}>
                {RA_AGENT_SHORT[a] || a} <span style={{ opacity: .65, fontWeight: 600 }}>{rows.filter(r => r.agents.includes(a)).length}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {allStates.length > 1 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "#5c6f5c", fontWeight: 700, marginBottom: 4 }}>State</div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            <button onClick={() => { setStPick(new Set()); setCtyPick(new Set()); }} style={sortBtn(stPick.size === 0)}>All</button>
            {allStates.map(s => (
              <button key={s} onClick={() => { setStPick(p => { const n = new Set(p); if (n.has(s)) n.delete(s); else n.add(s); return n; }); setCtyPick(new Set()); }} style={sortBtn(stPick.has(s))}>
                {s} <span style={{ opacity: .65, fontWeight: 600 }}>{rows.filter(r => r.states.includes(s)).length}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {allCounties.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "#5c6f5c", fontWeight: 700, marginBottom: 4 }}>
            County {ctyPick.size ? <span style={{ color: "#1f7a3d" }}>· {ctyPick.size} selected</span> : null}
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            <button onClick={() => setCtyPick(new Set())} style={sortBtn(ctyPick.size === 0)}>All</button>
            {allCounties.map(c => (
              <button key={c} onClick={() => setCtyPick(s => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; })} style={sortBtn(ctyPick.has(c))}>
                {c.charAt(0) + c.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <Stat k={null} lbl="PRF customers" val={totals.farmers} sub={`${built.baseYear} book`} />
        <Stat k="torenew" lbl="to renew" val={totals.notRenewed} />
        <Stat k="renewed" lbl={`written for ${built.renewalYear}`} val={totals.renewed} />
        {totals.shells > 0 && <Stat k="torenew" lbl="record opened, nothing elected" val={totals.shells} sub="carrier rolled the policy forward" />}
        <Stat k="nostart" lbl="nothing filed" val={totals.nothingFiled} />
        <Stat k={null} lbl="acres to renew" val={totals.baseAcres == null ? "—" : fmt(totals.baseAcres)} sub={totals.baseLiability == null ? null : fmtD0(totals.baseLiability) + " liability"} />
      </div>

      {/* NAMED ABSENCES. Everything the board meant to read and could not, on the
          page — never omitted. A folder that does not exist yet and a folder that
          exists and is empty are different facts and must not read the same. */}
      {(scanErr || docMissing || soiMissing || unknownFolders.length || unmatched.length) ? (
        <div style={{ fontSize: 11.5, color: "#7a6a4a", background: "#f9f6ef", border: "1px solid #e6ddc9", borderRadius: 9, padding: "9px 12px", marginBottom: 12, lineHeight: 1.7 }}>
          <b style={{ color: "#5c5030" }}>What this list could not read</b>
          {scanErr && <div>· Dropbox scan failed ({scanErr}) — the document column is blank for everyone, which is OUR gap, not theirs.</div>}
          {docMissing && <div>· No <b>{built.renewalYear} PRF</b> folder in Dropbox yet{prfRoots && prfRoots.searched ? ` (looked in ${prfRoots.searched.join(" and ")})` : ""} — nothing has been prepared for this renewal.</div>}
          {soiMissing && !docMissing && <div>· No <b>{built.renewalYear} PRF SOI</b> folder yet — schedules come back after the renewal is keyed.</div>}
          {unknownFolders.length > 0 && <div>· {unknownFolders.length} folder{unknownFolders.length === 1 ? "" : "s"} in the PRF root {unknownFolders.length === 1 ? "is" : "are"} not a known stage and {unknownFolders.length === 1 ? "its files are" : "their files are"} not ranked: {unknownFolders.slice(0, 4).join(" · ")}</div>}
          {unmatched.length > 0 && <div>· {unmatched.length} document{unmatched.length === 1 ? "" : "s"} in the folder name a policy number nobody in this list holds — {unmatched.slice(0, 6).join(" · ")}. A renewal written under a new number lands here until the sync sees it.</div>}
        </div>
      ) : null}

      {loading ? (
        <div style={{ padding: 40, color: "#677167", fontSize: 14 }}>Loading the PRF book…</div>
      ) : shown.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 12, padding: "36px 24px", textAlign: "center", color: "#60735f", fontSize: 13.5 }}>
          {rows.length ? "No customers match this filter." : `No PRF policies in ${built.baseYear} or ${built.renewalYear}.`}
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, overflowX: "auto" }}>
          {!isMobile && (
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr .8fr .7fr .8fr 1.3fr .7fr", gap: 8, background: "#eef4ea", borderBottom: "1px solid #c8d8c0", padding: "9px 14px", fontSize: 10, fontWeight: 800, color: "#4a6e4a", letterSpacing: ".05em", textTransform: "uppercase" }}>
              <div>Farmer</div><div>County</div><div>Agent</div><div>Acres</div>
              <div>{built.renewalYear} policy</div><div>Renewal / AR doc</div><div>SOI</div>
            </div>
          )}
          {shown.map((r, i) => (
            <div key={r.fid} style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: "1.6fr 1fr .8fr .7fr .8fr 1.3fr .7fr", gap: 8,
                                      padding: "10px 14px", borderBottom: i === shown.length - 1 ? "none" : "1px solid #eef2ec", fontSize: 12.5, alignItems: "center" }}>
              <div style={{ fontWeight: 700, color: "#1a2e1a" }}>{r.name}
                {r.aips.length ? <span style={{ fontSize: 10, color: "#677167", fontWeight: 600 }}> · {r.aips.map(a => a === "RAIN_AND_HAIL" ? "R&H" : a).join(", ")}</span> : null}
              </div>
              <div style={{ color: "#5c6f5c" }}>{r.counties.map(c => c.charAt(0) + c.slice(1).toLowerCase()).join(", ") || "—"}</div>
              <div style={{ color: "#5c6f5c" }}>{r.agents.map(a => RA_AGENT_SHORT[a] || a).join(" / ") || "—"}</div>
              <div style={{ fontVariantNumeric: "tabular-nums", color: "#3a4a38" }}>{r.base && r.base.acres != null ? fmt(r.base.acres) : "—"}</div>
              {/* THREE answers, not two. PRF carries over unless cancelled, so the
                  carrier opens next year's record on its own — long before sales
                  closing (Dec 1 for the following crop year). A record with nothing
                  elected on it is NOT a written renewal, and it is not "nothing"
                  either: knowing NAU has already opened the file changes the call
                  you make. See prfRenewalIsElected. */}
              <div>{r.renewed
                ? <span style={{ ...cellPill(RENEWAL_TONE.done) }}>✓ written</span>
                : r.renewalShell
                  ? <span style={{ ...cellPill(RENEWAL_TONE.todo) }} title="The carrier has opened a policy record for this crop year — PRF carries over unless cancelled — but nothing is elected on it yet: no coverage level, no acres, no premium and no intervals. It is not a written renewal.">◦ record opened · nothing elected</span>
                  : <span style={{ ...cellPill(RENEWAL_TONE.todo) }}>• not yet</span>}</div>
              <div>{stageCell(r.docStage)}</div>
              <div>{r.soiOnFile ? <span style={{ ...cellPill(RENEWAL_TONE.done) }}>✓ on file</span> : <span style={{ ...cellPill(RENEWAL_TONE.none) }}>—</span>}</div>
            </div>
          ))}
        </div>
      )}
      {scanning && <div style={{ fontSize: 11, color: "#677167", marginTop: 8 }}>Reading the Dropbox PRF folder…</div>}
    </div>
  );
}

// ── THE RH-5237 SHEET IS PULLED. It was a REPLICA, and that was the defect. ──
//
// Kevin, 2026-10-05, on being shown it: *"It's like the 426; don't make up your
// own replicas. If we are going to fill a form it needs to be the original form."*
// Then: *"Yes pull the replica."*
//
// What shipped was HTML laid out to look like RH-5237, and it was not close: the
// real form carries 14 lease rows (it had 2 blanks), an Apiculture terms column it
// omitted entirely, and a whole second page — the CERTIFICATION STATEMENT with its
// 18 U.S.C. criminal-penalty language, the NON-DISCRIMINATION STATEMENT and the
// PRIVACY ACT STATEMENT — that it dropped. A grower signs those.
//
// ⚠ THE RESOLVER UNDERNEATH IS NOT THE REPLICA AND IS DELIBERATELY KEPT.
// `api/_prf-lease-cert.js`, `src/prf/leaseCertResolve.js` and `leaseCertRollup.js`
// find the landowner under each insured field and decide owned-vs-leased; they are
// measured against the 2026-07-03 approved Gates result and guarded by 102
// assertions and 71 mutations. Nothing about them depends on how the sheet is drawn.
//
// WHAT UNBLOCKS IT: an UNENCRYPTED blank of R&H's own RH-5237. The real form is a
// proper fillable AcroForm — 108 fields, 14 rows, field names already captured, and
// a fill writes every value correctly. But the PDF R&H distributes is
// permission-encrypted: pdf-lib (which generates appearance streams) cannot open it
// at all, and pdfjs (which can decrypt and fill) writes no appearances and does not
// set NeedAppearances — the filled file was RENDERED and comes out visually blank.
// So this returns when R&H supplies an unlocked blank, or one is decrypted once.
//
// Until then there is NO button, because a form that looks official and is not is
// worse than no form — which is the whole of what Kevin said.

function RenewalRoster({ crop, onCrop, onQuote, isMobile = false }) {
  const cfg = CA_RENEWAL[crop] || CA_RENEWAL.CORN;
  // Which renewal list is open. PRF is a LINE OF BUSINESS, not a fourth entry in
  // CA_ORDER — that array drives the Quoter's crop dropdown and the Book Scan,
  // and CA_CROPS has no PRF row (no ARC/PLC lean, no price discovery window, no
  // contract month), so putting it there would break both.
  //
  // ⚠ A PERSISTED TAB IS UNTRUSTED INPUT — the same rule PRFAdvisorView already
  // carries. A value this build does not know matches no branch and renders the
  // pill row over empty space, which every static check calls clean. Validate
  // against the list that exists and fall back.
  const [lob, setLob] = useState(() => {
    try { const s = sessionStorage.getItem("cloverag_renewal_lob"); return (s === "crop" || s === "prf") ? s : "crop"; }
    catch { return "crop"; }
  });
  const setLobPersist = (v) => { try { sessionStorage.setItem("cloverag_renewal_lob", v); } catch {} setLob(v); };
  const [sel, setSel] = useState(() => new Set());   // farmer_ids checked for batch quoting
  // ── HAVE WE BEEN OUT TO SEE THEM ────────────────────────────────────────────
  // Kevin, 2026-10-03: *"in renewals add a manual check box where we can mark that we have
  // visited with a farm."* A row in `farm_visits` exists only where somebody ticked it, so
  // absence IS "not visited" — there is no third state here, unlike an insurance option
  // where "not elected" and "never captured" are different facts. Unticking DELETES rather
  // than writing false, so the table cannot grow two ways of saying no.
  //
  // ⚠ KEYED ON FARMER + SEASON + CROP YEAR. Seeing a grower in September about CY2027 wheat
  // says nothing about whether anyone has been out in January about CY2027 corn.
  const [visits, setVisits] = useState({});          // farmer_id → { visited_at }
  const [visitErr, setVisitErr] = useState(null);
  // Customer locations, for the route planner only. AGENT SURFACE: the Coverage Advisor is
  // never rendered in the portal (its "coverage" key routes to a farmer's own coverage), so
  // this read belongs here and nowhere a grower can reach — which is exactly the rule the MI
  // map had to be fixed for breaking.
  const [geo, setGeo] = useState({});                // farmer_id → { lat, lon, address }
  // farmer_id → { rma: [pt], jd: [pt] }. The fallback for a farm whose address cannot be
  // geocoded; resolved at plan time by resolveFarmLocations, never written to farmers.lat.
  const [fieldPts, setFieldPts] = useState({});
  // Which of Kevin's three seasons this list is, derived from the line of business and the
  // crop rather than stored: the roster already knows both, and a second source for the same
  // answer is how a tick comes to be filed against a season the page is not showing.
  const visitSeason = lob === "prf" ? "prf" : (cfg.season === "FALL" ? "fall" : "spring");
  const [year, setYear] = useState(() => caRenewalYear(cfg.season));
  // ⚠ THIS MUST SIT BELOW `year`, AND A DEP ARRAY IS WHY. The body is a closure and would
  // have been fine anywhere, but `[visitSeason, year]` EVALUATES IMMEDIATELY — so declared
  // above the useState it read `year` in its temporal dead zone and the whole Coverage
  // Advisor threw "Cannot access 'v' before initialization" at paint. It compiled, and
  // check-tdz named the line. Same shape CLAUDE.md records for a useCallback dep array.
  //
  // The working days left in this season, from the SAME rule the pace strip draws, so the
  // planner and the strip cannot disagree about how much season is left. The planner uses it
  // only to say whether its plan fits.
  const visitDaysLeft = useMemo(() => {
    const calYear = visitSeason === "spring" ? year : year - 1;
    const p = renewalPaceRule({ total: 1, visited: 0, today: new Date().toISOString().slice(0, 10), seasonKey: visitSeason, calendarYear: calYear });
    return p.ok ? p.workingDaysLeft : null;
  }, [visitSeason, year]);
  const [cov, setCov] = useState([]);
  const [pols, setPols] = useState([]);
  const [names, setNames] = useState({});
  const [docs, setDocs] = useState(null);          // { application:{stages}, production:{…}, acreage:{…} }
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(true);
  const [scanErr, setScanErr] = useState(null);
  const [only, setOnly] = useState(null);          // null | "open" | "done" | "nostart"
  const [stagePick, setStagePick] = useState(null); // { step, stage } — "who is in this box"
  const [ctyPick, setCtyPick] = useState(() => new Set());   // county filter (empty = all)
  const [stPick, setStPick] = useState(() => new Set());     // state filter (empty = all)
  const [agPick, setAgPick] = useState(() => new Set());     // agent filter (empty = all)
  const [sort, setSort] = useState({ col: "name", dir: "asc" });

  // The derived year follows the crop tab, but only until it's touched — a manual
  // pick shouldn't be stomped when switching crops back and forth.
  const [yearTouched, setYearTouched] = useState(false);
  useEffect(() => { if (!yearTouched) setYear(caRenewalYear(cfg.season)); }, [cfg.season, yearTouched]);
  // A stage that held 40 growers on one crop/year can hold none on the next, and a
  // pick that survives the switch reads as an empty book rather than a stale filter.
  useEffect(() => { setStagePick(null); }, [crop, year]);
  // ── THE VISITS FOR THIS SEASON AND YEAR ─────────────────────────────────────
  // ⚠ RE-RUNS ON BOTH. A tick belongs to one (season, crop year) pair, so a list left
  // showing last year's visits after the year picker moved would mark farms done that
  // nobody has been out to see — and the pace above it would count them.
  //
  // ⚠ AND IT CLEARS FIRST. Without that, the previous season's ticks stay on screen for the
  // length of the fetch, which on a slow connection is long enough to read and act on.
  useEffect(() => {
    let alive = true;
    setVisits({}); setVisitErr(null);
    (async () => {
      try {
        const v = await sbFetch("farm_visits", {
          select: "farmer_id,visited_at",
          filters: [`season=eq.${visitSeason}`, `crop_year=eq.${year}`],
        });
        if (!alive) return;
        const m = {}; (v || []).forEach(r => { if (r.farmer_id) m[r.farmer_id] = { visited_at: r.visited_at }; });
        setVisits(m);
      } catch (e) {
        // ⚠ A FAILED READ IS NOT "NOBODY HAS BEEN VISITED". Until sql/085 is applied this
        // table does not exist and every read 404s; saying so is the difference between a
        // missing feature and a season that looks untouched.
        if (alive) setVisitErr(`Visit records could not be read (${e.message}) — no farm is marked visited below, which is NOT the same as nobody having been seen.`);
      }
    })();
    return () => { alive = false; };
  }, [visitSeason, year]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [c, p, f, pfl, jdf] = await Promise.all([
          // ⚠ `net_premium` RIDES ALONG FOR THE PRIOR YEAR, NOT THIS ONE. Kevin, 2026-10-04:
          // *"I like to get my biggest customers done first ... premium would be the better
          // number."* The RENEWAL year cannot answer that — measured on the live CY2027 wheat
          // roster, net_premium is 0 on all 115 coverages, because a renewal is not priced
          // until sales closing. Ranked on it, every customer tied at zero and the ordering
          // was noise that still looked like an answer. So size is what they actually PAID
          // last year, which is also the honest reading of "my biggest customers".
          sbFetch("crop_coverage", { select: "policy_id,county,crop,acres,liability,coverage_level,aph_yield,net_premium" }),
          sbFetch("policies", { select: "id,farmer_id,policy_number,crop_year,agent_name,agent_name_override,state" }),
          // lat/lon/address ride along for the route planner. AGENT SURFACE — the
          // Coverage Advisor is never rendered in the portal, so a customer location here is
          // not the MI-map defect in a second place.
          sbFetch("farmers", { select: "id,name,lat,lon,address" }),
          // ⚠ THE FALLBACK LOCATIONS. 75 of 282 farms carry no usable lat/lon and the
          // address half cannot be geocoded — Census resolves 2 of the 27 that HAVE an
          // address, because TIGER has no ranges for rural WNY roads. A farm's insured
          // ground places 9 more, and they are the largest operations in the book
          // (Howlett, Meisenzahl, Panek, CY Farms, Woodlawn, Rauberdale). Both reads are
          // narrow — only rows that actually carry a coordinate — so this is ~1k + ~4k
          // rows, not the whole of policy_fields. See src/renewals/farmLocation.js for why
          // the median and never the mean, and why this is never written to farmers.lat.
          sbFetch("policy_fields", { select: "farmer_id,latitude,longitude", filters: ["latitude=not.is.null"] }),
          sbFetch("jd_fields", { select: "farmer_id,centroid_lat,centroid_lon", filters: ["centroid_lat=not.is.null"] }),
        ]);
        if (!alive) return;
        const nm = {}, gg = {};
        (f || []).forEach(r => {
          if (!r.id) return;
          nm[r.id] = r.name;
          gg[r.id] = { lat: r.lat, lon: r.lon, address: r.address };
        });
        setCov(c || []); setPols(p || []); setNames(nm); setGeo(gg);
        setFieldPts(groupFieldPoints({ policyFields: pfl || [], jdFields: jdf || [] }));
      } catch (e) { if (alive) console.error("Renewal roster load failed:", e); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Live Dropbox status, fetched after the roster renders so the list isn't blocked
  // on it. summary=1 keeps the per-file listing off the wire — this page wants the
  // rollup, not the folder dump.
  useEffect(() => {
    let alive = true;
    (async () => {
      setScanning(true); setScanErr(null);
      try {
        const r = await fetch(`/api/market?fn=dropbox&action=stage-scan&doc=all&year=${year}&season=${cfg.season}&summary=1`,
          { method: "POST", headers: getApiAuthHeaders() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        if (alive) setDocs(d.docs || {});
      } catch (e) { if (alive) { setScanErr(e.message); setDocs(null); } }
      finally { if (alive) setScanning(false); }
    })();
    return () => { alive = false; };
  }, [year, cfg.season]);

  const pcore = (s) => { const runs = String(s || "").match(/\d{6,}/g); return runs && runs.length ? runs.sort((a, b) => b.length - a.length)[0] : null; };

  const rows = useMemo(() => {
    const want = new Set(cfg.crops);
    const polById = {}; (pols || []).forEach(p => { polById[p.id] = p; });
    // farmer → every policy core they hold, across ALL years. Cores are year-stable
    // (NY-944-6101410-26 and -27 share 6101410), and a production report filed for
    // the 2027 renewal is NUMBERED -26 because it reports the prior year's actuals —
    // so scoping cores to the renewal year would drop exactly those files.
    const coresBy = {};
    // Every policy id the farmer holds, ALL years — the batch printer reads
    // production_history by policy_id, so dropping prior-year policies would quote
    // them off an empty yield history.
    const pidsBy = {};
    (pols || []).forEach(p => {
      if (!p.farmer_id) return;
      const c = pcore(p.policy_number);
      if (c) (coresBy[p.farmer_id] = coresBy[p.farmer_id] || new Set()).add(c);
      (pidsBy[p.farmer_id] = pidsBy[p.farmer_id] || []).push(p.id);
    });

    // ── Who owns the renewal ─────────────────────────────────────────────────
    // A manual AOR is stored per policy_number in `agent_name_override`, and the
    // renewal year's policy is a DIFFERENT number (…-27 vs …-26), so an override
    // set last year is not on this year's row. Carried across years by stem —
    // the same restatement Premium Analytics does — or moving a book to MM KK
    // would read as every renewal reverting to the written agent each July.
    const ovrByStem = buildAgentOverrideStems(pols);
    // Built from ALL policies, not just this crop year: the whole point is to
    // reach an earlier year of the same policy stem.
    const carryByStem = buildAgentCarryStems(pols);
    const agentOf = (p) => policyAgentOf(p, ovrByStem, carryByStem);
    const provOf = (p) => policyAgentProvenance(p, ovrByStem, carryByStem);

    // ── what each customer paid LAST year, for the visit order ────────────────
    // Across EVERY crop, not just the one on this tab: "my biggest customers" is a statement
    // about the whole relationship, and a dairy whose wheat is small may be the largest
    // account in the book. A farm with no prior-year policy is left UNDEFINED rather than
    // set to 0 — new business is not a small customer, it is an unknown one, and the two
    // must not sort together.
    const priorPrem = {};
    (cov || []).forEach(cc => {
      const p = polById[cc.policy_id];
      if (!p || p.crop_year !== year - 1 || !p.farmer_id) return;
      if (cc.net_premium == null) return;
      priorPrem[p.farmer_id] = (priorPrem[p.farmer_id] || 0) + Number(cc.net_premium);
    });

    const byFarmer = {};
    (cov || []).forEach(cc => {
      if (!want.has(String(cc.crop || "").toUpperCase())) return;
      const p = polById[cc.policy_id];
      if (!p || p.crop_year !== year || !p.farmer_id) return;
      const g = byFarmer[p.farmer_id] || (byFarmer[p.farmer_id] = {
        fid: p.farmer_id, name: names[p.farmer_id] || "—",
        counties: new Set(), states: new Set(), acres: null, liability: null, agents: new Set(), provs: new Set(), lines: 0,
        // undefined where we hold no prior year, never 0 — see priorPrem above
        priorPremium: Object.prototype.hasOwnProperty.call(priorPrem, p.farmer_id) ? priorPrem[p.farmer_id] : null,
      });
      g.lines++;
      if (p.state) g.states.add(String(p.state).toUpperCase());
      if (cc.county) g.counties.add(String(cc.county).toUpperCase());
      g.agents.add(agentOf(p));
      g.provs.add(provOf(p));
      // NULL acres is NOT zero — for a fall crop it means acreage reporting hasn't
      // happened yet (wheat's ARD is Nov 15). Summing it as 0 would invent a fact.
      if (cc.acres != null) g.acres = (g.acres || 0) + Number(cc.acres);
      if (cc.liability != null) g.liability = (g.liability || 0) + Number(cc.liability);
    });

    const stageFor = (fid, dk) => {
      const map = docs && docs[dk] && docs[dk].stages;
      if (!map) return null;
      const cores = coresBy[fid];
      if (!cores) return null;
      let best = null;
      cores.forEach(c => {
        const st = map[c];
        if (!st || !(st in RENEWAL_RANK)) return;          // unknown stage → ignore, never guess a rank
        if (best === null || RENEWAL_RANK[st] > RENEWAL_RANK[best]) best = st;
      });
      // A folder that failed to list could hold this farmer further along —
      // say "stage unknown" rather than report the lower stage we did see.
      if (stageIsBlind(best, stageScanBlindRank(docs[dk], RENEWAL_RANK), RENEWAL_RANK)) return RENEWAL_UNKNOWN;
      return best;
    };

    return Object.values(byFarmer).map(g => {
      const steps = {};
      RENEWAL_STEPS.forEach(s => { steps[s.key] = stageFor(g.fid, s.key); });
      const doneN = RENEWAL_STEPS.filter(s => RENEWAL_DONE.has(steps[s.key])).length;
      const anyN = RENEWAL_STEPS.filter(s => steps[s.key]).length;
      const states = [...g.states].sort();
      // The wheat rate sets loaded are NY/PA/MD/OH only. stFips() falls back to "36"
      // for anything else, so an Indiana policy would quote at NEW YORK rates and
      // look perfectly plausible. Refuse to offer the quote instead of printing a
      // confident wrong number.
      const quotable = states.length > 0 && states.some(s => CA_QUOTE_STATES.has(s));
      const offRate = states.filter(s => !CA_QUOTE_STATES.has(s));
      // Roster order first, buckets last — so a farmer split across two agents
      // reads "Meisenzahl / Other", never "Other / Meisenzahl".
      const agents = [...g.agents].sort((a, b) => RA_AGENT_ORDER.indexOf(a) - RA_AGENT_ORDER.indexOf(b));
      // A farm can hold policies in more than one state at once, so the marker is
      // "any of these was carried" and the title says which cases are present.
      const provs = [...g.provs];
      const agentCarried = provs.includes("carried");
      const agentTitle = agents.join(" · ") + (provs.length ? " — " + provs.map(pv => RA_PROV_NOTE[pv] || pv).join("; ") : "");
      return { ...g, counties: [...g.counties].sort(), states, quotable, offRate,
               agents, agentCarried, agentTitle, steps, doneN, anyN, policyIds: pidsBy[g.fid] || [] };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [cov, pols, names, docs, year, cfg.crops]);

  // Counties + states present in this crop's book, for the filter buttons.
  const allCounties = useMemo(() => {
    const s = new Set();
    rows.forEach(r => (stPick.size ? r.states.some(x => stPick.has(x)) : true) && r.counties.forEach(c => s.add(c)));
    return [...s].sort();
  }, [rows, stPick]);
  const allStates = useMemo(() => {
    const s = new Set();
    rows.forEach(r => r.states.forEach(x => s.add(x)));
    return [...s].sort((a, b) => (a === "NY" ? -1 : b === "NY" ? 1 : a.localeCompare(b)));
  }, [rows]);
  // Agents actually on this crop's renewal book, in roster order. Data-driven, so
  // an agent with nothing to renew this season doesn't sit there as a dead pill.
  const allAgents = useMemo(() => {
    const s = new Set();
    rows.forEach(r => r.agents.forEach(a => s.add(a)));
    return RA_AGENT_ORDER.filter(a => s.has(a));
  }, [rows]);
  // Drop county picks that don't exist on the crop you just switched to, or the
  // list silently filters to nothing.
  useEffect(() => {
    setCtyPick(prev => {
      const keep = new Set([...prev].filter(c => allCounties.includes(c)));
      return keep.size === prev.size ? prev : keep;
    });
  }, [allCounties]);
  // Same for agents — Harris may write wheat and no soybeans, and a pick that
  // survives the crop switch shows an empty list rather than her (empty) book.
  useEffect(() => {
    setAgPick(prev => {
      const keep = new Set([...prev].filter(a => allAgents.includes(a)));
      return keep.size === prev.size ? prev : keep;
    });
  }, [allAgents]);

  // Sort value per column. Status columns sort by ladder rank with "nothing filed"
  // lowest, so ascending puts the work that hasn't started at the top.
  const sortVal = (r, col) => {
    if (col === "name") return r.name;
    // Roster order, not alphabetical — grouping the book by agent is the point, and
    // A→Z would scatter "No agent" into the middle of it.
    if (col === "agent") return r.agents.length ? RA_AGENT_ORDER.indexOf(r.agents[0]) : 99;
    if (col === "county") return r.counties[0] || "";
    if (col === "acres") return r.acres == null ? -1 : r.acres;
    if (col === "progress") return r.doneN * 10 + r.anyN;
    const st = r.steps[col];
    return st ? (RENEWAL_RANK[st] ?? -1) : -1;
  };
  // Same column again flips direction; a new column starts ascending — which for a
  // status column means least-advanced first, i.e. the work to do at the top.
  const clickSort = (col) => setSort(s => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }));

  // Book filters — geography and agent. Split out from `shown` because the progress
  // bars read from it: measuring completion AFTER the status filter is circular —
  // click "still open" and every bar would read 0% done by construction. Agent
  // belongs on THIS side of the split on purpose, so picking Meisenzahl re-scopes
  // the three bars to his book and the % answers "how far along am I", not "how far
  // along is the agency".
  const scoped = useMemo(() => rows.filter(r => {
    if (stPick.size && !r.states.some(x => stPick.has(x))) return false;
    if (ctyPick.size && !r.counties.some(c => ctyPick.has(c))) return false;
    if (agPick.size && !r.agents.some(a => agPick.has(a))) return false;
    return true;
  }), [rows, stPick, ctyPick, agPick]);

  const shown = useMemo(() => {
    const out = scoped.filter(r => {
      if (only === "done" && r.doneN !== RENEWAL_STEPS.length) return false;
      if (only === "open" && r.doneN === RENEWAL_STEPS.length) return false;
      if (only === "nostart" && r.anyN > 0) return false;
      if (stagePick) {
        const st = r.steps[stagePick.step] || RENEWAL_NONE;
        if (st !== stagePick.stage) return false;
      }
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    return out.sort((a, b) => {
      const av = sortVal(a, sort.col), bv = sortVal(b, sort.col);
      const c = typeof av === "string" ? String(av).localeCompare(String(bv)) : (av - bv);
      return c ? c * dir : a.name.localeCompare(b.name);   // ties always settle A→Z
    });
  }, [scoped, only, sort, stagePick]);

  // ── Where each farm is, for the route ─────────────────────────────────────
  // The mailing address wins where we have one; a farm whose address cannot be geocoded
  // falls back to the MEDIAN of its insured fields, labelled as such and never as the
  // farmstead. 75 of 282 farms carry no usable lat/lon and the Census geocoder can place
  // only 2 of the 27 that have an address at all, so without this the schedule prints a
  // quarter of the book — including Howlett, Panek, Meisenzahl and CY Farms — as "could
  // not be placed". See src/renewals/farmLocation.js for the median-not-mean rule and the
  // measured accuracy of a centroid against a known address.
  const visitFarms = useMemo(() => {
    const roster = shown.filter(r => !visits[r.fid]).map(r => ({
      id: r.fid, name: r.name,
      // What they paid last year, which is what orders the DAYS — never which farms share
      // one. A farm with no prior year carries null and sorts after the known ones rather
      // than tying with the smallest.
      size: r.priorPremium == null ? null : r.priorPremium,
      county: r.counties && r.counties.length ? r.counties[0] : null,
      lat: geo[r.fid] ? geo[r.fid].lat : null,
      lon: geo[r.fid] ? geo[r.fid].lon : null,
      address: geo[r.fid] ? geo[r.fid].address : null,
    }));
    const res = resolveFarmLocations(roster, fieldPts);
    return roster.map(f => {
      const loc = res.byFarmer[f.id];
      if (!loc) return f;
      return { ...f, lat: loc.lat, lon: loc.lon,
               locSource: loc.source, locLabel: loc.label, locFallback: loc.fallback,
               locFieldCount: loc.n };
    });
  }, [shown, visits, geo, fieldPts]);

  // ── Where the season stands, one bar per document ──────────────────────────
  // "Done" is KEYED and nothing else — same rule the chips use, because a signed
  // report the AIP hasn't got is still work.
  //
  // The rest of the bar is broken out by the SAME tones as the chips rather than
  // pooled into one block, because pooling them lies: at the open of a season the
  // pre-prints go out to everyone at once, so a pooled bar reads 0% done under a
  // full-looking stripe when not one thing has come back. Only `keyed` is painted
  // solid; the other stages are tints, so the bar answers "how much is green"
  // and the colour tells you WHOSE move it is — ours (amber) or theirs (blue).
  //
  // Alongside the tone rollup the bar draws, each step carries its exact per-STAGE
  // counts: the tones say how the season is moving, the stages say which folder to
  // open. Only stages actually holding someone get a box — an empty ladder rung is
  // noise — except `keyed`, which is always shown because it's the target.
  const stepBars = useMemo(() => RENEWAL_STEPS.map(s => {
    const n = scoped.length;
    const by = { done: 0, work: 0, wait: 0, todo: 0, none: 0, unknown: 0 };
    const byStage = {};
    scoped.forEach(r => {
      const st = r.steps[s.key], meta = st ? RENEWAL_STAGE[st] : null;
      by[st === RENEWAL_UNKNOWN ? "unknown" : meta ? meta.tone : "none"]++;
      const k = st || RENEWAL_NONE;
      byStage[k] = (byStage[k] || 0) + 1;
    });
    const stages = Object.keys(RENEWAL_STAGE)
      .filter(k => byStage[k] || k === "keyed")
      .sort((a, b) => RENEWAL_RANK[b] - RENEWAL_RANK[a])
      .map(k => ({ stage: k, n: byStage[k] || 0, ...RENEWAL_STAGE[k] }));
    if (byStage[RENEWAL_UNKNOWN]) stages.push({ stage: RENEWAL_UNKNOWN, n: byStage[RENEWAL_UNKNOWN], tone: "none", lbl: "Stage unknown", txt: "a Dropbox folder could not be read — the real stage may be further along" });
    if (byStage[RENEWAL_NONE]) stages.push({ stage: RENEWAL_NONE, n: byStage[RENEWAL_NONE], tone: "none", lbl: "Nothing filed", txt: "the scan found no document for this farmer" });
    const dead = !!scanErr || !!(docs && docs[s.key] && (docs[s.key].error || docs[s.key].root_missing));
    return { key: s.key, label: s.label, n, by, stages,
             pct: n ? Math.round((by.done / n) * 100) : 0,
             w: (t) => (n ? (by[t] / n) * 100 : 0),
             dead, waiting: !docs && scanning };
  }), [scoped, docs, scanning, scanErr]);

  // Counted over `scoped`, the same set the bars measure — filter to Keenan and the
  // cards have to say how many of HIS renewals are open, or the page shows two
  // different books at once and the honest-looking number is the wrong one.
  const totals = useMemo(() => ({
    farmers: scoped.length,
    done: scoped.filter(r => r.doneN === RENEWAL_STEPS.length).length,
    nostart: scoped.filter(r => r.anyN === 0).length,
    acres: scoped.reduce((s, r) => s + (r.acres || 0), 0),
    acresKnown: scoped.filter(r => r.acres != null).length,
    filtered: scoped.length !== rows.length,
  }), [scoped, rows]);

  const Chip = ({ st }) => {
    if (st === RENEWAL_UNKNOWN) return (
      <span title="A Dropbox folder could not be read — the real stage may be further along"
        style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", minWidth:26, padding:"2px 7px", borderRadius:999,
                 background:"#fdf1f0", color:"#a03028", border:"1px solid #f0c9c5", fontSize:12, fontWeight:800, lineHeight:1.5 }}>?</span>
    );
    const meta = st ? RENEWAL_STAGE[st] : null;
    const tone = RENEWAL_TONE[meta ? meta.tone : "none"];
    return (
      <span title={meta ? meta.txt : "nothing in Dropbox for this step"}
        style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", minWidth:26, padding:"2px 7px", borderRadius:999,
                 background:tone.bg, color:tone.fg, border:"1px solid "+tone.bd, fontSize:12, fontWeight:800, lineHeight:1.5 }}>
        {tone.mark}
      </span>
    );
  };
  // The two filters answer different questions ("how far along overall" vs "who is
  // sitting in this folder"), and stacking them silently would show an empty table
  // with no clue which one emptied it. Picking either clears the other.
  const pickStage = (step, stage) =>
    setStagePick(p => { setOnly(null); return (p && p.step === step && p.stage === stage) ? null : { step, stage }; });
  const StatCard = ({ k, lbl, val, sub }) => (
    <button onClick={() => { setStagePick(null); setOnly(only === k ? null : k); }} disabled={!k}
      style={{ textAlign:"left", padding:"9px 12px", borderRadius:10, cursor: k ? "pointer" : "default",
               border:"1px solid " + (only === k ? "#2d4a2d" : "#d5e2d5"), background: only === k ? "#eaf3ea" : "#fff", minWidth:104 }}>
      <div style={{ fontSize:17, fontWeight:800, color:"#1a2e1a", fontVariantNumeric:"tabular-nums" }}>{val}</div>
      <div style={{ fontSize:10.5, color:"#5c6f5c", fontWeight:700 }}>{lbl}</div>
      {sub ? <div style={{ fontSize:10.5, color:"#677167" }}>{sub}</div> : null}
    </button>
  );

  const acresPending = totals.acresKnown === 0 && scoped.length > 0;

  // The batch quote engine is wheat-only (2027 pilot) — same limit the Quoter states.
  // Show the honest reason rather than a button that silently prints nothing.
  const canQuote = crop === "WHEAT";
  const selRows = shown.filter(r => sel.has(r.fid));
  // Select-all only ever picks rows that can actually be quoted.
  const pickable = shown.filter(r => r.quotable && r.policyIds.length);
  const allShown = pickable.length > 0 && pickable.every(r => sel.has(r.fid));
  const toggle = (fid) => setSel(s => { const n = new Set(s); if (n.has(fid)) n.delete(fid); else n.add(fid); return n; });
  // ── TICKING A VISIT ─────────────────────────────────────────────────────────
  // ⚠ OPTIMISTIC, AND IT PUTS THE TICK BACK IF THE WRITE FAILS. A checkbox that stays
  // ticked after a refused write is the worst version of this: the agent believes the farm
  // is done, the pace counts it, and nobody finds out until the season closes short. The
  // error is NAMED on screen rather than swallowed.
  const toggleVisit = async (fid) => {
    const had = !!visits[fid];
    const when = new Date().toISOString().slice(0, 10);
    setVisitErr(null);
    setVisits(v => { const n = { ...v }; if (had) delete n[fid]; else n[fid] = { visited_at: when }; return n; });
    try {
      if (had) {
        // ⚠ sbDelete TAKES A QUERY STRING, NOT AN ARRAY of filters the way sbFetch does.
        // An array stringifies to `a,b,c`, which PostgREST reads as no filter at all — and a
        // DELETE with no filter is every visit in the table. Checked against the helper
        // rather than assumed from the sibling API.
        await sbDelete("farm_visits", `farmer_id=eq.${fid}&season=eq.${visitSeason}&crop_year=eq.${year}`);
      } else {
        await sbUpsert("farm_visits",
          [{ farmer_id: fid, season: visitSeason, crop_year: year, visited_at: when, updated_at: new Date().toISOString() }],
          "farmer_id,season,crop_year");
      }
    } catch (e) {
      setVisits(v => { const n = { ...v }; if (had) n[fid] = { visited_at: when }; else delete n[fid]; return n; });
      setVisitErr(`Couldn't save that visit: ${e.message}`);
    }
  };
  const toggleAll = () => setSel(s => {
    const n = new Set(s);
    if (allShown) pickable.forEach(r => n.delete(r.fid)); else pickable.forEach(r => n.add(r.fid));
    return n;
  });
  const quoteSelected = () => {
    // Shaped exactly like the Quoter's own farm objects so the one batch printer
    // consumes both without caring where the selection came from.
    const picks = {};
    selRows.forEach(r => { if (r.policyIds.length) picks[r.name] = { name: r.name, allPolicyIds: r.policyIds, _allPolicyIds: r.policyIds }; });
    if (onQuote) onQuote(picks);
  };

  // One list per line of business. Defined once and rendered by BOTH branches
  // below, so the tab you switched away from is never missing on the way back.
  const pillOn = (on) => ({
    fontSize:12.5, fontWeight:700, padding:"6px 14px", borderRadius:999, cursor:"pointer",
    border:"1px solid " + (on ? "#2d4a2d" : "#d5e2d5"), background: on ? "#2d4a2d" : "#fff", color: on ? "#fff" : "#5c6f5c",
  });
  const pills = (
    <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
      {CA_ORDER.map(k => (
        <button key={k} onClick={() => { setLobPersist("crop"); onCrop && onCrop(k); }} style={pillOn(lob === "crop" && crop === k)}>
          {CA_CROPS[k].label} Renewals
        </button>
      ))}
      <button onClick={() => setLobPersist("prf")} style={pillOn(lob === "prf")}>PRF Renewals</button>
    </div>
  );

  // PRF's roster is built on a different substrate (the PRF book, not
  // crop_coverage) and a different year rule, so it is its own component rather
  // than a branch threaded through every memo below.
  if (lob === "prf") return <div>{pills}<PRFRenewalRoster isMobile={isMobile} /></div>;

  return (
    <div>
      {pills}

      <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap", marginBottom:12 }}>
        <div>
          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:4 }}>Renewal year</div>
          <select value={year} onChange={e => { setYearTouched(true); setYear(+e.target.value); }}
            style={{ fontSize:14, padding:"7px 10px", borderRadius:7, border:"1px solid #d5e2d5", background:"#fff" }}>
            {[year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).sort().map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:4 }}>Sort by</div>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {[["name","Farmer"],["agent","Agent"],["county","County"],["acres","Acres"],["progress","Progress"]].map(([k, lbl]) => (
              <button key={k} onClick={() => clickSort(k)} style={sortBtn(sort.col === k)}>
                {lbl}{sort.col === k ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
              </button>
            ))}
          </div>
        </div>
      </div>

      {allAgents.length > 1 && (
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:4 }}>Agent</div>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            <button onClick={() => setAgPick(new Set())} style={sortBtn(agPick.size === 0)}>All</button>
            {allAgents.map(a => {
              const n = rows.filter(r => r.agents.includes(a)).length;
              return (
                <button key={a} onClick={() => setAgPick(p => { const n2 = new Set(p); if (n2.has(a)) n2.delete(a); else n2.add(a); return n2; })}
                  style={sortBtn(agPick.has(a))}
                  title={a === RA_UNATTRIBUTED ? "no agent resolves for this policy — either written outside the agency roster, or the carrier has named none on any year of it. Hover a row to see which."
                         : a}>
                  {RA_AGENT_SHORT[a] || a} <span style={{ opacity:.65, fontWeight:600 }}>{n}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {allStates.length > 1 && (
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:4 }}>State</div>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            <button onClick={() => { setStPick(new Set()); setCtyPick(new Set()); }} style={sortBtn(stPick.size === 0)}>All</button>
            {allStates.map(s => {
              const n = rows.filter(r => r.states.includes(s)).length;
              return (
                <button key={s} onClick={() => { setStPick(p => { const n2 = new Set(p); if (n2.has(s)) n2.delete(s); else n2.add(s); return n2; }); setCtyPick(new Set()); }}
                  style={sortBtn(stPick.has(s))} title={CA_QUOTE_STATES.has(s) ? "" : "no wheat rate set loaded — not quotable"}>
                  {s} <span style={{ opacity:.65, fontWeight:600 }}>{n}</span>{CA_QUOTE_STATES.has(s) ? "" : " ⚠"}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {allCounties.length > 1 && (
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:4 }}>
            County {ctyPick.size ? <span style={{ color:"#1f7a3d" }}>· {ctyPick.size} selected</span> : null}
          </div>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            <button onClick={() => setCtyPick(new Set())} style={sortBtn(ctyPick.size === 0)}>All</button>
            {allCounties.map(c => (
              <button key={c} onClick={() => setCtyPick(s => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; })}
                style={sortBtn(ctyPick.has(c))}>
                {titleCaseName(c)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12 }}>
        <StatCard k={null} lbl={`${CA_CROPS[crop].label} customers`} val={totals.farmers}
          sub={`${cfg.season.toLowerCase()} ${year}${totals.filtered ? ` · of ${rows.length}` : ""}`} />
        <StatCard k="open" lbl="still open" val={totals.farmers - totals.done} />
        <StatCard k="nostart" lbl="nothing filed" val={totals.nostart} />
        <StatCard k="done" lbl="complete" val={totals.done} />
      </div>

      {/* Season progress — one bar per document, over the CURRENT geography filter. */}
      <div style={{ display:"grid", gap:9, marginBottom:12,
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(230px, 1fr))" }}>
        {stepBars.map(b => (
          <div key={b.key} style={{ border:"1px solid #d5e2d5", borderRadius:10, background:"#fff", padding:"10px 12px" }}>
            <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8 }}>
              <div style={{ fontSize:11, fontWeight:800, color:"#2d4a2d", textTransform:"uppercase", letterSpacing:".05em" }}>{b.label}</div>
              <div style={{ fontSize:19, fontWeight:800, fontVariantNumeric:"tabular-nums",
                            color: (b.dead || b.waiting || !b.n) ? "#b7c7b7" : "#1a2e1a" }}>
                {b.waiting ? "…" : (b.dead || !b.n) ? "—" : `${b.pct}%`}
              </div>
            </div>
            <div style={{ display:"flex", height:9, borderRadius:999, background:"#eef3ee", overflow:"hidden", margin:"7px 0 6px" }}
              title={b.n ? RENEWAL_BAR_ORDER.filter(t => b.by[t]).map(t => `${b.by[t]} ${RENEWAL_BAR[t].lbl}`)
                             .concat(b.by.none ? [`${b.by.none} nothing filed`] : []).concat(b.by.unknown ? [`${b.by.unknown} stage unknown`] : []).join(" · ") : ""}>
              {RENEWAL_BAR_ORDER.map(t => (
                <div key={t} style={{ width:`${b.dead || b.waiting ? 0 : b.w(t)}%`, background:RENEWAL_BAR[t].fill, transition:"width .3s" }} />
              ))}
            </div>
            {b.waiting || b.dead || !b.n ? (
              <div style={{ fontSize:10.5, color:"#5c6f5c" }}>
                {b.waiting ? "checking Dropbox…" : b.dead ? "Dropbox status unavailable" : "no customers in this filter"}
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                {b.stages.map(sg => {
                  const on = stagePick && stagePick.step === b.key && stagePick.stage === sg.stage;
                  return (
                    <button key={sg.stage} onClick={() => sg.n && pickStage(b.key, sg.stage)} disabled={!sg.n} title={sg.txt}
                      style={{ display:"flex", alignItems:"center", gap:7, width:"100%", textAlign:"left",
                               padding:"3px 6px", borderRadius:6, fontSize:11, lineHeight:1.5,
                               border:"1px solid " + (on ? "#2d4a2d" : "transparent"),
                               background: on ? "#eaf3ea" : "transparent",
                               color: sg.n ? "#3f523f" : "#b7c7b7", cursor: sg.n ? "pointer" : "default" }}>
                      <span style={{ width:9, height:9, borderRadius:3, flex:"0 0 auto",
                                     background: sg.n ? (RENEWAL_BAR[sg.tone] ? RENEWAL_BAR[sg.tone].fill : "#e7eee7") : "#eef3ee" }} />
                      <span style={{ fontWeight:800, fontVariantNumeric:"tabular-nums", minWidth:24 }}>{sg.n}</span>
                      <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{sg.lbl}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {stagePick && (
        <div style={{ display:"flex", alignItems:"center", gap:9, flexWrap:"wrap", marginBottom:10,
                      padding:"7px 12px", borderRadius:8, background:"#eaf3ea", border:"1px solid #cfe3cf", fontSize:12, color:"#2d4a2d" }}>
          <strong>{shown.length}</strong>
          {shown.length === 1 ? " farmer" : " farmers"} ·&nbsp;
          <strong>{(RENEWAL_STEPS.find(s => s.key === stagePick.step) || {}).label}</strong>
          &nbsp;→&nbsp;
          <strong>{stagePick.stage === RENEWAL_NONE ? "Nothing filed" : stagePick.stage === RENEWAL_UNKNOWN ? "Stage unknown" : (RENEWAL_STAGE[stagePick.stage] || {}).lbl}</strong>
          <button onClick={() => setStagePick(null)}
            style={{ marginLeft:"auto", fontSize:11, padding:"4px 10px", borderRadius:6, border:"1px solid #cfe3cf", background:"#fff", color:"#5c6f5c", cursor:"pointer" }}>
            show all
          </button>
        </div>
      )}

      {scanErr && (
        <div style={{ padding:"8px 12px", borderRadius:8, background:"#fdf1f0", border:"1px solid #f0c9c5", color:"#a03028", fontSize:12, marginBottom:10 }}>
          Dropbox status unavailable — <strong>{scanErr}</strong>. The customer list below is still accurate; only the three status columns are blank.
        </div>
      )}
      {!scanErr && docs && RENEWAL_STEPS.some(s => docs[s.key] && docs[s.key].error) && (
        <div style={{ padding:"8px 12px", borderRadius:8, background:"#fdf9ec", border:"1px solid #e8d5a8", color:"#7a5a00", fontSize:12, marginBottom:10 }}>
          {RENEWAL_STEPS.filter(s => docs[s.key] && docs[s.key].error).map(s => `${s.label}: ${docs[s.key].error}`).join(" · ")}
        </div>
      )}
      {!scanErr && docs && RENEWAL_STEPS.some(s => docs[s.key] && docs[s.key].root_missing) && (
        <div style={{ padding:"8px 12px", borderRadius:8, background:"#eef3f8", border:"1px solid #cddcea", color:"#3a5570", fontSize:12, marginBottom:10 }}>
          No Dropbox folder yet for {RENEWAL_STEPS.filter(s => docs[s.key] && docs[s.key].root_missing).map(s => s.label).join(", ")} — that step reads blank until the folder exists.
        </div>
      )}
      {!scanErr && docs && RENEWAL_STEPS.some(s => docs[s.key] && (docs[s.key].listing_errors || []).length) && (
        <div style={{ padding:"8px 12px", borderRadius:8, background:"#fdf1f0", border:"1px solid #f0c9c5", color:"#a03028", fontSize:12, marginBottom:10 }}>
          Some Dropbox folders could not be read, so farms that may be further along show <strong>?</strong> (stage unknown):{" "}
          {RENEWAL_STEPS.flatMap(s => ((docs[s.key] && docs[s.key].listing_errors) || []).map(e => `${s.label}: ${String(e.folder || "").split("/").pop() || e.folder}`)).join(" · ")}
        </div>
      )}
      {acresPending && (
        <div style={{ padding:"8px 12px", borderRadius:8, background:"#f6f8f6", border:"1px solid #d5e2d5", color:"#5c6f5c", fontSize:12, marginBottom:10 }}>
          Acres are blank because acreage reporting for {cfg.season.toLowerCase()} {year} hasn't happened yet — <strong>not</strong> missing data.
        </div>
      )}

      {canQuote ? (
        <div style={{ display:"flex", gap:9, alignItems:"center", flexWrap:"wrap", marginBottom:9 }}>
          <button onClick={quoteSelected} disabled={!selRows.length}
            style={{ fontSize:12.5, fontWeight:800, padding:"7px 14px", borderRadius:7, border:"none",
                     background: selRows.length ? "#2d6a2d" : "#b7c7b7", color:"#fff", cursor: selRows.length ? "pointer" : "default" }}>
            🖨 Quote {selRows.length || ""} selected
          </button>
          {selRows.length > 0 && (
            <button onClick={() => setSel(new Set())}
              style={{ fontSize:11.5, padding:"6px 10px", borderRadius:7, border:"1px solid #d5e2d5", background:"#f2f6f2", color:"#5c6f5c", cursor:"pointer" }}>clear</button>
          )}
          <span style={{ fontSize:11, color:"#677167" }}>
            Opens the Quoter with these farms loaded — press Print there to build one document.
          </span>
        </div>
      ) : (
        <div style={{ fontSize:11.5, color:"#677167", marginBottom:9 }}>
          Batch quoting is wheat-only for now (2027 pilot); {CA_CROPS[crop].label.toLowerCase()} plugs in once its ADM rate set is extracted.
        </div>
      )}

      {/* ── THE SEASON'S PACE, AND THE ROUTE THROUGH WHAT IS LEFT ──────────────
          Both read the SAME `shown` list the table below renders, so a filter the agent has
          applied — a county, an agent, a stage — narrows the plan too. That is the behaviour
          worth having: "three a day through Genesee" is a real question and "three a day
          through a list I am not looking at" is not.
          ⚠ The route is planned over the OUTSTANDING farms only. Routing somebody already
          ticked would put a farm on a schedule that has been visited. */}
      {visitErr && (
        <div style={{ margin:"0 0 10px", padding:"9px 11px", border:"1px solid #e8d8a8", background:"#fff8e8", borderRadius:8, fontSize:12, color:"#8a6d1f" }}>
          {visitErr}
        </div>
      )}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) minmax(0,1.2fr)", gap:12, marginBottom:12 }}>
        <VisitPace seasonKey={visitSeason} cropYear={year} total={rows.length} visited={rows.filter(r => visits[r.fid]).length} isMobile={isMobile} />
        {/* ⚠ THE PACE CARD AND THE ROUTE COUNT DIFFERENT THINGS. The pace strip is handed
            `rows` — every outstanding customer — while the route is handed `shown`, which a
            filter chip can narrow. A live screenshot read "124 still to see" beside "47
            farms planned" with nothing reconciling them, which looks exactly like the
            planner silently dropping 77 farms. It is told the unfiltered total so it can
            say which it is. */}
        <VisitRoutePlanner isMobile={isMobile} workingDaysLeft={visitDaysLeft} seasonKey={visitSeason} cropYear={year}
          farms={visitFarms} outstandingTotal={rows.filter(r => !visits[r.fid]).length} />
      </div>
      <div style={{ overflowX:"auto", border:"1px solid #d5e2d5", borderRadius:10, background:"#fff" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:isMobile ? 700 : 0 }}>
          <thead>
            <tr style={{ background:"#f2f6f2" }}>
              {canQuote && (
                <th style={{ width:32, padding:"9px 0 9px 12px", background:"#f2f6f2" }}>
                  <input type="checkbox" checked={allShown} onChange={toggleAll} title="Select all shown" />
                </th>
              )}
              <th style={{ textAlign:"left", padding:"9px 12px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#5c6f5c", position:"sticky", left:0, background:"#f2f6f2" }}>Farmer</th>
              {/* Kevin, 2026-10-03: a manual tick that we have been out to see them. It sits
                  beside the farm name rather than at the end of eight stage columns, because
                  it is the one thing on this row a human asserts rather than the pipeline. */}
              <th style={{ textAlign:"center", padding:"9px 8px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#5c6f5c", whiteSpace:"nowrap" }}>Visited</th>
              <th onClick={() => clickSort("county")} style={{ textAlign:"left", padding:"9px 10px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#5c6f5c", cursor:"pointer" }}>
                County{sort.col === "county" ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
              </th>
              <th style={{ textAlign:"left", padding:"9px 8px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#5c6f5c" }}>St</th>
              <th onClick={() => clickSort("agent")} style={{ textAlign:"left", padding:"9px 10px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#5c6f5c", cursor:"pointer", whiteSpace:"nowrap" }}>
                Agent{sort.col === "agent" ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
              </th>
              <th style={{ textAlign:"right", padding:"9px 10px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", color:"#5c6f5c" }}>Acres</th>
              {RENEWAL_STEPS.map(s => (
                <th key={s.key} style={{ textAlign:"center", padding:"9px 10px", fontSize:10.5, textTransform:"uppercase", letterSpacing:".05em", whiteSpace:"nowrap",
                                         color: stagePick && stagePick.step === s.key ? "#1f5c2e" : "#5c6f5c",
                                         background: stagePick && stagePick.step === s.key ? "#eaf3ea" : "#f2f6f2" }}>{s.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6 + RENEWAL_STEPS.length + (canQuote ? 1 : 0)} style={{ padding:"20px 12px", color:"#5c6f5c" }}>Loading customers…</td></tr>
            ) : !shown.length ? (
              <tr><td colSpan={6 + RENEWAL_STEPS.length + (canQuote ? 1 : 0)} style={{ padding:"20px 12px", color:"#5c6f5c" }}>
                {rows.length ? "No customers match this filter." : `No ${CA_CROPS[crop].label.toLowerCase()} customers on the ${year} book yet.`}
              </td></tr>
            ) : shown.map(r => (
              <tr key={r.fid} style={{ borderTop:"1px solid #eef3ee", background: sel.has(r.fid) ? "#f4faf4" : "#fff" }}>
                {canQuote && (
                  <td style={{ padding:"8px 0 8px 12px" }}>
                    <input type="checkbox" checked={sel.has(r.fid)} onChange={() => toggle(r.fid)} disabled={!r.policyIds.length || !r.quotable}
                      title={!r.policyIds.length ? "no policies on file to quote from"
                             : !r.quotable ? `No rate set for ${r.offRate.join(", ")} — quoting it would price off New York` : ""} />
                  </td>
                )}
                <td style={{ padding:"8px 12px", fontWeight:600, position:"sticky", left:0, background: sel.has(r.fid) ? "#f4faf4" : "#fff" }}>
                  {r.name}
                  {r.doneN === RENEWAL_STEPS.length && <span style={{ marginLeft:6, fontSize:10, fontWeight:800, color:"#1f7a3d" }}>done</span>}
                </td>
                {/* The visit tick. It writes straight through — see toggleVisit, which puts
                    the box back and NAMES the error if the write is refused, because a tick
                    that silently did not save reads as a farm somebody has already been to. */}
                <td style={{ padding:"8px 8px", textAlign:"center" }}>
                  <input type="checkbox" checked={!!visits[r.fid]} onChange={() => toggleVisit(r.fid)}
                    title={visits[r.fid] ? `visited ${visits[r.fid].visited_at}` : "mark that we have been out to see this farm about this renewal"}
                    style={{ cursor:"pointer", width:16, height:16 }} />
                </td>
                <td style={{ padding:"8px 10px", color:"#5c6f5c", fontSize:12 }}>{r.counties.join(", ") || "—"}</td>
                <td style={{ padding:"8px 8px", fontSize:11.5, color: r.quotable ? "#5c6f5c" : "#a5461b", fontWeight: r.quotable ? 600 : 800, whiteSpace:"nowrap" }}
                  title={r.quotable ? "" : `No ${CA_CROPS[crop].label.toLowerCase()} rate set for ${r.offRate.join(", ")} — can't be quoted here`}>
                  {r.states.join("/") || "—"}{r.quotable ? "" : " ⚠"}
                </td>
                {/* AMBER IS FOR A GAP THAT IS OURS TO CLOSE, and the carrier not
                    having written a renewal yet is not one. Painting every
                    unresolved row amber marked 150 CY2027 policies as problems on
                    a page whose whole job is to show what still needs doing.
                    A CARRIED name is marked with a degree sign rather than a
                    colour: it is a real answer, just not one the carrier has
                    restated for this crop year yet. */}
                <td style={{ padding:"8px 10px", fontSize:11.5, whiteSpace:"nowrap",
                             color: r.agents.length === 1 && r.agents[0] === RA_UNATTRIBUTED ? "#a5461b" : "#5c6f5c" }}
                  title={r.agentTitle || r.agents.join(" · ")}>
                  {r.agents.map(a => RA_AGENT_SHORT[a] || a).join(" / ") || "—"}
                  {r.agentCarried ? <span style={{ color:"#677167", fontWeight:700 }} title="carried forward from an earlier crop year of the same policy — the carrier has not restated it for this year">°</span> : null}
                </td>
                <td style={{ padding:"8px 10px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{r.acres == null ? <span style={{ color:"#677167" }}>—</span> : fmt(r.acres)}</td>
                {RENEWAL_STEPS.map(s => (
                  <td key={s.key} style={{ padding:"8px 10px", textAlign:"center" }}><Chip st={r.steps[s.key]} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display:"flex", gap:12, flexWrap:"wrap", alignItems:"center", marginTop:8, fontSize:11, color:"#5c6f5c" }}>
        <span>{shown.length} of {rows.length} shown</span>
        {scanning && <span style={{ color:"#677167" }}>· checking Dropbox…</span>}
        <span style={{ flex:1 }} />
        {/* Driven off RENEWAL_BAR so the legend, the bars and the boxes can't drift
            into three different words for the same state again. */}
        {RENEWAL_BAR_ORDER.concat("none").map(t => (
          <span key={t} style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
            <span style={{ color:RENEWAL_TONE[t].fg, fontWeight:800 }}>{RENEWAL_TONE[t].mark}</span>
            {t === "none" ? "nothing filed" : RENEWAL_BAR[t].lbl}
          </span>
        ))}
      </div>
      <div style={{ marginTop:6, fontSize:11, color:"#677167" }}>
        Status is read from Dropbox. Filing, signing and keying all happen in File Management.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COVERAGE BOOK SCAN
// ═══════════════════════════════════════════════════════════════════════════
// "Who doesn't have TA elected?" · "How many farms carry ECO on wheat?" · "Who is
// still on basic units?" — one crop year of the book, sliced on any combination of
// what the policies actually ELECTED.
//
// The filter chrome deliberately mirrors Premium Analytics — facet chips, options
// derived from the data in view rather than a hardcoded list, Top-N with the tail
// folded into an Other row instead of truncated. The FACT LAYER is not shared and
// should not be: Premium Analytics is a two-source money model spanning
// MPCI/PRF/LRP/DRP across two crop years, and this is one row per base MPCI
// coverage in ONE year with its SCO/ECO overlays folded in.
//
// ── THE M13 FILE IS THE SOURCE OF TRUTH, WHATEVER IT GIVES US ──────────────
// Kevin, 2026-08-17, settling this screen's one open question. Two halves, and
// the second is not a hedge on the first:
//
// 1. WHERE THE FILE SPEAKS, IT IS THE ANSWER — including when it says nothing.
//    `crop_coverage.insurance_options` is P15 `f[38]`, read straight out of the
//    M13/NAU file by both syncs. A BLANK field on a synced row means no option was
//    elected. Full stop — not "probably", not "pending a statement". We do not
//    reason our way to an election the AIP already states, and we do not go
//    looking for an SOI to second-guess the file that the book reconciles to.
//    (The book agrees, for what it is worth: all 15 CY2026 wheat coverages with
//    acres and a blank string still carry an approved APH, production history and
//    unit rows, so the P15 resolved and f[38] was simply empty. That is a
//    consistency check, not the reason — the reason is that it is the AIP's file.)
//
// 2. WHERE THE FILE SAYS NOTHING AT ALL, THAT IS "MISSING" — never "no".
//    The hand-loaded books (Agri Sompo, ProAg) are not on an M13 feed. They were
//    typed from SOI PDFs, which never carried the field: Agri Sompo has it on 0 of
//    70 CY2026 rows, ProAg on 2 of 76. M13 gives us nothing for them, so nothing
//    is what we report. Filling that blank with "doesn't have it" would be
//    inventing an AIP answer, which is the one thing this repo never does — and it
//    would put whole farms on a call list for a product they may already own.
//
// Hence three states per option — elected / not elected / not captured — decided
// by PROVENANCE (is this a sync-format policy number, i.e. did it come out of an
// AIP file) and never by a ratio of how much a carrier happens to report.
// The codes we hold a MEANING for, in the order an agent reads them. Anything the
// file carries beyond this list is still shown — appended, and rendered RAW —
// because a label invented to fill a gap cannot be told from a sourced one. The
// live CY2026 files carry EI, QB, PR, ST and EO on top of these; none of them is
// guessed at here.
const CBS_OPT_ORDER = ["TA", "YA", "YE", "YC", "QL", "MC", "CP", "PF"];
const CBS_UNIT_LABEL = { EU: "Enterprise (EU)", BU: "Basic (BU)", OU: "Optional (OU)", UA: "Unit Area (UA)" };
// Tri-state chip values. One declaration each, on its own line, because
// check-election-rules extracts them from this file by source and a comma-joined
// declaration is invisible to that — which would print VOID, not a false pass.
const CBS_HAS = "has";
const CBS_NO = "missing";
const CBS_UNCAPTURED = "not captured";

// Is this a supplemental overlay rather than a base MPCI coverage? SCO ships as
// plan 'ARPI' on older years (see the SCO/ARPI note in the farmer card) — the test
// has to carry both or CY2025 reads as a book with no SCO at all.
const cbsIsSco = (plan) => /SCO|ARPI/i.test(plan || "");
const cbsIsEco = (plan) => /ECO/i.test(plan || "");
const cbsIsOverlay = (plan) => cbsIsSco(plan) || cbsIsEco(plan);

// One fact per base coverage row: the elections, the overlays that belong to it,
// and enough scope (farm/agent/carrier/county) to filter on.
function buildElectionFacts({ policies, coverage, units, farmerRows, year }) {
  const nameById = {};
  (farmerRows || []).forEach(f => { if (f && f.id) nameById[f.id] = f.name; });

  const pols = (policies || []).filter(p => p && +p.crop_year === +year);
  const polById = {};
  pols.forEach(p => { polById[p.id] = p; });
  const ovrByStem = buildAgentOverrideStems(policies);
  const carryByStem = buildAgentCarryStems(policies);

  // Unit structure is NULL on the summary row far more often than it should be
  // (234 of 680 CY2026 NAU RP rows), because it is only stamped when the group is
  // created. The per-unit rows carry it, so fill the gap from them — majority
  // wins, and a coverage whose units disagree stays unknown rather than guessing.
  const unitStructByCov = {};
  (units || []).forEach(u => {
    if (!u || +u.crop_year !== +year || !u.unit_structure) return;
    const k = [u.policy_id, (u.crop || "").toUpperCase(), (u.county || "").toUpperCase()].join("|");
    const m = unitStructByCov[k] = unitStructByCov[k] || {};
    m[u.unit_structure] = (m[u.unit_structure] || 0) + 1;
  });
  const structFromUnits = (row) => {
    const m = unitStructByCov[[row.policy_id, (row.crop || "").toUpperCase(), (row.county || "").toUpperCase()].join("|")];
    if (!m) return null;
    const ranked = Object.entries(m).sort((a, b) => b[1] - a[1]);
    return ranked.length && (ranked.length === 1 || ranked[0][1] > ranked[1][1]) ? ranked[0][0] : null;
  };

  const mine = (coverage || []).filter(c => c && polById[c.policy_id]);
  const base = mine.filter(c => !cbsIsOverlay(c.plan));
  const sco  = mine.filter(c => cbsIsSco(c.plan));
  const eco  = mine.filter(c => cbsIsEco(c.plan));

  // Whether a BLANK options field is allowed to mean "nothing elected" is decided
  // by where the row came from, not by the carrier's name and not by a ratio.
  //
  // The first cut of this asked "did this carrier report options on ANY row this
  // year" — and ProAg reports them on 2 of its 76 rows, which flipped the whole
  // carrier to captured and would have read its 820 wheat acres as "no TA" when
  // the truth is nobody typed it. Provenance is the sound test: a sync-format
  // policy came out of the AIP file, which carries P15 f[38], so a blank there is
  // the file saying no option. A hand-loaded policy came off an SOI PDF, which
  // never carried the field at all.
  //
  // Note this is per ROW, not per carrier — the handful of hand-loaded rows that
  // DO carry options still answer "has it", because someone typed a real election
  // off a statement. Only the blanks differ.

  // matchSupplementals is scoped by the caller to ONE county — the county lives on
  // the section, not the row — and takes the county's full base list so an untyped
  // (whole-crop) endorsement is owned by exactly one base row instead of rendering
  // under every type of the crop.
  const byCounty = {};
  base.forEach(c => {
    const k = [c.policy_id, (c.county || "").toUpperCase()].join("|");
    (byCounty[k] = byCounty[k] || []).push(c);
  });

  const facts = [];
  Object.entries(byCounty).forEach(([k, rows]) => {
    const scoHere = sco.filter(s => [s.policy_id, (s.county || "").toUpperCase()].join("|") === k);
    const ecoHere = eco.filter(s => [s.policy_id, (s.county || "").toUpperCase()].join("|") === k);
    rows.forEach(row => {
      const p = polById[row.policy_id];
      const sup = matchSupplementals(scoHere, ecoHere, row, rows);
      const crop = (row.crop || "").toUpperCase();
      const codes = parseOptionCodes(row.insurance_options);
      // EU rides in the options string on the hand-loaded books and is the only
      // unit-structure signal those rows carry. Stored column first, then the
      // per-unit majority, then that.
      const unitStructure = row.unit_structure || structFromUnits(row) || (codes.includes("EU") ? "EU" : null);
      const ecoBand = sup.eco.map(e => (e.coverage_level != null ? Math.round(Number(e.coverage_level) * 100) : null))
                            .filter(v => v != null).sort((a, b) => b - a)[0] ?? null;
      const scoBand = sup.sco.map(e => (e.coverage_level != null ? Math.round(Number(e.coverage_level) * 100) : null))
                             .filter(v => v != null).sort((a, b) => b - a)[0] ?? null;
      facts.push({
        key: [row.policy_id, crop, (row.county || "").toUpperCase(), row.type_code || "", row.practice_code || ""].join("|"),
        farmerId: p.farmer_id,
        farm: nameById[p.farmer_id] || "—",
        policyNumber: p.policy_number || "—",
        aip: p.aip || "—",
        agent: policyAgentOf(p, ovrByStem, carryByStem),
        state: p.state || "—",
        county: (row.county || "—").toUpperCase(),
        crop,
        season: FALL_CROPS_SET.has(crop) ? "Fall" : "Spring",
        typeLabel: crop === "CORN" ? cornTypeLabel(row.type_code) : (row.type || null),
        practice: row.practice || null,
        plan: row.plan || "—",
        planCode: row.mpci_plan_code || null,
        level: row.coverage_level != null ? Math.round(Number(row.coverage_level) * 100) : null,
        priceElectionPct: row.price_election_pct != null ? Number(row.price_election_pct) : null,
        // COVERAGE TYPE — 'C' is CAT, 'A' is additional (buy-up). This is the
        // only field that distinguishes them: a CAT policy and a 50% buy-up
        // share a plan AND a coverage level, so without it CAT is invisible on
        // this screen. That invisibility is why a CAT guarantee could read 45%
        // short for as long as it did — nobody could list who had CAT to check.
        // NOT derived from price_election_pct: 0.55 is the CONSEQUENCE of CAT,
        // and a reduced ECO/SCO election lands at 0.5-0.9 without being CAT.
        coverageType: (row.coverage_type || "").trim().toUpperCase() || null,
        unitStructure,
        unitCount: row.unit_count != null ? Number(row.unit_count) : null,
        aph: row.aph_yield != null ? Number(row.aph_yield) : null,
        acres: Number(row.acres) || 0,
        ppAcres: Number(row.pp_acres) || 0,
        uiAcres: Number(row.ui_acres) || 0,
        liability: Number(row.liability) || 0,
        premium: Number(row.net_premium) || 0,
        grossPremium: Number(row.total_premium) || 0,
        codes,
        // ── CAPTURED = the feed actually reached this row ────────────────────
        // Provenance alone is not enough, and 28 rows proved it on 2026-08-17.
        // A sync-FORMAT policy number says the row is shaped like ours; it does
        // not say the file still carries it. Nine NAU policies in the book
        // (NY-944-6090376, PA-942-*, MD-942-*, 28 coverages) are absent from
        // 953616-2026.dat entirely — a different NAU book or dropped policies —
        // so the sync cannot fill their options no matter how correct it is, and
        // calling them "none elected" asserts an election nobody sent us.
        //
        // The observable signal is LIABILITY — RMA having KEYED the line, which
        // is the same event that makes the coverage real enough to carry an
        // election. Measured, not assumed:
        //   unit_count > 0   true on all 28 unreachable rows — separates nothing
        //                    (a rolled APH database carries a unit count and
        //                    nothing else). Tried; do not put it back.
        //   acres > 0        also true on the 2 unit-"0099" placeholder lines,
        //                    which are acreage REPORTED but not yet keyed: no
        //                    liability, no premium, no APH, and a coverage id
        //                    that resolves to no P14, so there is nothing for
        //                    them to inherit options from. Calling those "none
        //                    elected" states an election that was never keyed.
        //   liability > 0    true for exactly the coverages RMA has priced.
        // Rows that DO carry codes are unaffected either way — this only decides
        // what a BLANK means.
        optionsCaptured: isSyncFormatPolicyNumber(p.policy_number) && Number(row.liability) > 0,
        sco: sup.sco.length > 0,
        scoBand,
        eco: sup.eco.length > 0,
        ecoBand,
        scoPremium: sup.sco.reduce((s, r) => s + (Number(r.net_premium) || 0), 0),
        ecoPremium: sup.eco.reduce((s, r) => s + (Number(r.net_premium) || 0), 0),
      });
    });
  });
  return facts;
}

// An option's state on one fact. The uncaptured case is per CARRIER, not per row.
function cbsOptState(f, code) {
  if (f.codes.includes(code)) return CBS_HAS;
  return f.optionsCaptured ? CBS_NO : CBS_UNCAPTURED;
}

// ── THE FARM-LEVEL RESOLVER — ONE RULE, THREE CALLERS ───────────────────────
// The board counts, the filter chips and the drill-down all have to answer
// "does this farm have X on this crop" the SAME way, or the screen contradicts
// itself: on 2026-08-18 the board was farm-level while the chips were still
// per-coverage, so the ECO column said 27 farms lack it and cycling the ECO chip
// handed back Michael J Meisenzahl's Livingston wheat — a coverage with no ECO
// on a farm that plainly has ECO on wheat.
//
// SCO/ECO are elected per crop x county, so a farm really can carry one in two
// counties and not a third. That makes the per-coverage answer TRUE and useless
// for a call list. A farm HAS it when any of its coverages does.
function cbsFarmHas(farmFacts, pred) { return (farmFacts || []).some(pred); }

// Three-state at the farm: an election anywhere wins; failing that, a coverage
// the feed actually REACHED that did not elect it is a real "no"; only when
// neither holds is the farm genuinely uncaptured.
function cbsFarmOptState(farmFacts, code) {
  const fs = farmFacts || [];
  if (fs.some(f => cbsOptState(f, code) === CBS_HAS)) return CBS_HAS;
  if (fs.some(f => cbsOptState(f, code) === CBS_NO))  return CBS_NO;
  return CBS_UNCAPTURED;
}

// Group facts by farm once; every farm-level answer above reads this.
function cbsByFarm(facts) {
  const m = new Map();
  (facts || []).forEach(f => {
    const cur = m.get(f.farmerId) || [];
    cur.push(f);
    m.set(f.farmerId, cur);
  });
  return m;
}

// CAT vs buy-up, as its own dimension. RMA writes CAT at a 50% coverage level —
// and so does a 50% buy-up, so the LEVEL cannot tell them apart, and neither can
// the plan. Only crop_coverage.coverage_type can: 'C' = CAT, 'A' = additional
// (buy-up). Anything else renders raw rather than being folded into one of the
// two, the same convention the option and stage codes follow.
//
// Added 2026-08-19 because CAT was invisible on this screen, and that is a large
// part of why a CAT guarantee reading 45% short survived as long as it did:
// there was no way to list who had CAT in order to check them.
const covTypeLabel = (f) =>
  f.coverageType === "C" ? "CAT"
  : f.coverageType === "A" ? "Buy-up"
  : f.coverageType ? `code ${f.coverageType}` : "Not reported";

// "50% CAT" vs a bare "50%". The coverage LEVEL alone cannot say which, because
// CAT is written at 50% and so is a 50% buy-up — and the difference is not
// cosmetic: CAT also elects 55% of the price, so the same level carries a
// guarantee ~45% lower.
//
// Until 2026-08-19 only the Prevent Plant tab appended the marker. The Revenue
// Guarantee table — the one an agent actually reads — printed a bare "50%" on
// nine farms' CAT coverage, including Marquart's and My-T Acres' potatoes.
// One function, four call sites, so these cannot drift the way the price-election
// math did (that expression existed seven times and three were right).
const covLevelText = (level, coverageType) =>
  level == null ? "—" : `${(Number(level) * 100).toFixed(0)}%${coverageType === "C" ? " CAT" : ""}`;

const CBS_DIMS = [
  { key: "farm",     label: "Farm",     get: f => f.farm },
  { key: "covType",  label: "Coverage", get: f => covTypeLabel(f) },
  { key: "crop",     label: "Crop",     get: f => f.crop },
  { key: "county",   label: "County",   get: f => f.county },
  { key: "state",    label: "State",    get: f => f.state },
  { key: "aip",      label: "Carrier",  get: f => f.aip },
  { key: "agent",    label: "Agent",    get: f => f.agent },
  { key: "plan",     label: "Plan",     get: f => f.plan },
  { key: "unit",     label: "Units",    get: f => f.unitStructure || "Not reported" },
  { key: "level",    label: "Level",    get: f => (f.level != null ? `${f.level}%` : "—") },
  { key: "season",   label: "Season",   get: f => f.season },
];

function CoverageBookScan({ isMobile = false }) {
  const [year, setYear]   = useState(CURRENT_CROP_YEAR);
  const [raw, setRaw]     = useState(null);
  const [loading, setLoad] = useState(true);
  const [err, setErr]     = useState(null);

  const [seasonSel, setSeasonSel] = useState(() => new Set());
  const [cropSel, setCropSel]     = useState(() => new Set());
  const [stateSel, setStateSel]   = useState(() => new Set());
  const [countySel, setCountySel] = useState(() => new Set());
  const [aipSel, setAipSel]       = useState(() => new Set());
  const [agentSel, setAgentSel]   = useState(() => new Set());
  const [planSel, setPlanSel]     = useState(() => new Set());
  const [unitSel, setUnitSel]     = useState(() => new Set());
  const [levelSel, setLevelSel]   = useState(() => new Set());
  // CAT vs buy-up. Its own facet because coverage_level cannot express it —
  // CAT is 50%, and so is a 50% buy-up.
  const [covTypeSel, setCovTypeSel] = useState(() => new Set());
  const [optSel, setOptSel]       = useState(() => ({}));   // code → CBS_HAS | CBS_NO | CBS_UNCAPTURED
  const [scoSel, setScoSel]       = useState(null);         // null | CBS_HAS | CBS_NO
  const [ecoSel, setEcoSel]       = useState(null);
  const [facetOpen, setFacetOpen] = useState(() => ({}));

  const [groupBy, setGroupBy] = useState("crop");
  // Drill-down: { label, rows } — WHO is behind a number. Every count on this
  // screen is a headcount over farms, and a headcount an agent cannot open is a
  // number they have to take on trust. Opening it in place beats filtering the
  // whole page away, because the comparison you were reading stays on screen.
  const [drill, setDrill]     = useState(null);
  const [query, setQuery]     = useState("");
  const [sortCol, setSortCol] = useState("farm");
  const [sortDir, setSortDir] = useState("asc");
  const [rowLimit, setRowLimit] = useState(60);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoad(true); setErr(null);
      try {
        const [policies, coverage, units, farmerRows] = await Promise.all([
          sbFetch("policies", { select: "id,farmer_id,policy_number,crop_year,aip,state,county,agent_name,agent_name_override" }),
          // coverage_type is what makes CAT VISIBLE. Without it a CAT policy is
          // indistinguishable here from a 50% buy-up — same plan, same level —
          // and that invisibility is why a CAT guarantee could read 45% short
          // for as long as it did (see electedPrice / guaranteeBushels).
          // price_election_pct alone is not enough: it is the CONSEQUENCE of the
          // coverage type, and a reduced ECO/SCO election also lands at 0.5-0.9.
          sbFetch("crop_coverage", { select: "policy_id,county,crop,plan,mpci_plan_code,practice,practice_code,type,type_code,coverage_level,price_election_pct,coverage_type,unit_structure,unit_count,insurance_options,aph_yield,acres,pp_acres,ui_acres,liability,total_premium,net_premium" }),
          sbFetch("crop_coverage_units", { select: "policy_id,crop_year,crop,county,unit_structure" }),
          sbFetch("farmers", { select: "id,name" }),
        ]);
        if (!alive) return;
        setRaw({ policies, coverage, units, farmerRows });
      } catch (e) { if (alive) setErr(String((e && e.message) || e)); }
      finally { if (alive) setLoad(false); }
    })();
    return () => { alive = false; };
  }, []);

  const years = useMemo(() => {
    if (!raw) return [];
    return [...new Set((raw.policies || []).map(p => +p.crop_year).filter(Boolean))].sort((a, b) => b - a);
  }, [raw]);

  const facts = useMemo(() => (raw ? buildElectionFacts({ ...raw, year }) : []), [raw, year]);

  // The hand-loaded books can't answer an option question at all. Named and
  // counted, because the alternative is a filter that quietly returns those farms
  // as "doesn't have it" and sends someone to sell a product they may own.
  const uncaptured = useMemo(() => {
    const rows = facts.filter(f => !f.optionsCaptured);
    return {
      aips: [...new Set(rows.map(f => f.aip))].sort(),
      farms: new Set(rows.map(f => f.farmerId)).size,
      acres: rows.reduce((s, f) => s + f.acres, 0),
    };
  }, [facts]);

  // SCOPE first — these are genuinely per-coverage questions (which crop, which
  // county, which carrier). They decide WHICH coverages are in play.
  const baseScope = useMemo(() => facts.filter(f =>
    (!seasonSel.size || seasonSel.has(f.season)) &&
    (!cropSel.size   || cropSel.has(f.crop)) &&
    (!stateSel.size  || stateSel.has(f.state)) &&
    (!countySel.size || countySel.has(f.county)) &&
    (!aipSel.size    || aipSel.has(f.aip)) &&
    (!agentSel.size  || agentSel.has(f.agent)) &&
    (!planSel.size   || planSel.has(f.plan)) &&
    (!unitSel.size   || unitSel.has(f.unitStructure || "Not reported")) &&
    (!levelSel.size  || levelSel.has(f.level != null ? `${f.level}%` : "—")) &&
    (!covTypeSel.size || covTypeSel.has(covTypeLabel(f))) &&
    (!query.trim() || f.farm.toLowerCase().includes(query.trim().toLowerCase()) ||
                      f.policyNumber.toLowerCase().includes(query.trim().toLowerCase()))
  ), [facts, seasonSel, cropSel, stateSel, countySel, aipSel, agentSel, planSel, unitSel, levelSel, covTypeSel, query]);

  // ELECTION filters resolve at the FARM, over that scope — the same rule the
  // board counts with, so a chip and a column can never disagree. Filtering
  // per coverage is what put a farm's ECO-less county back on screen after the
  // board had correctly ruled the farm as having ECO.
  //
  // Note the composition: "wheat + doesn't have ECO" means farms with no ECO on
  // any WHEAT coverage, not farms with no ECO anywhere. Scope narrows first.
  const scoped = useMemo(() => {
    const active = scoSel != null || ecoSel != null || Object.values(optSel).some(Boolean);
    if (!active) return baseScope;
    const byFarm = cbsByFarm(baseScope);
    const keep = new Set();
    byFarm.forEach((fs, id) => {
      if (scoSel != null && (scoSel === CBS_HAS) !== cbsFarmHas(fs, f => f.sco)) return;
      if (ecoSel != null && (ecoSel === CBS_HAS) !== cbsFarmHas(fs, f => f.eco)) return;
      for (const [code, want] of Object.entries(optSel)) {
        if (want && cbsFarmOptState(fs, code) !== want) return;
      }
      keep.add(id);
    });
    return baseScope.filter(f => keep.has(f.farmerId));
  }, [baseScope, scoSel, ecoSel, optSel]);

  const facetOpts = useCallback((dimKey) => {
    const g = CBS_DIMS.find(d => d.key === dimKey).get;
    return [...new Set(facts.map(g))].filter(v => v && v !== "—").sort((a, b) =>
      dimKey === "level" ? (parseFloat(b) - parseFloat(a)) : String(a).localeCompare(String(b)));
  }, [facts]);

  const toggleSet = (setter) => (v) => setter(prev => {
    const n = new Set(prev);
    n.has(v) ? n.delete(v) : n.add(v);
    return n;
  });
  // Options cycle rather than toggle, because the question is as often "who does
  // NOT have this" as "who does" — that is the whole point of the screen.
  const cycleOpt = (code) => setOptSel(prev => {
    const cur = prev[code] || null;
    const next = cur === null ? CBS_HAS : cur === CBS_HAS ? CBS_NO : cur === CBS_NO ? CBS_UNCAPTURED : null;
    return { ...prev, [code]: next };
  });
  const cycleTri = (cur, set) => () => set(cur === null ? CBS_HAS : cur === CBS_HAS ? CBS_NO : null);

  const resetAll = () => {
    [setSeasonSel, setCropSel, setStateSel, setCountySel, setAipSel, setAgentSel, setPlanSel, setUnitSel, setLevelSel, setCovTypeSel]
      .forEach(s => s(new Set()));
    setOptSel({}); setScoSel(null); setEcoSel(null); setQuery("");
  };
  const anyFilter = seasonSel.size || cropSel.size || stateSel.size || countySel.size || aipSel.size ||
    agentSel.size || planSel.size || unitSel.size || levelSel.size || covTypeSel.size || scoSel || ecoSel || query.trim() ||
    Object.values(optSel).some(Boolean);

  // ── Totals. Farms are counted DISTINCT — a grower with corn in three counties
  // is one farm, and "how many farms have ECO" is a headcount question.
  const totals = useMemo(() => {
    const farms = new Set(scoped.map(f => f.farmerId));
    return {
      farms: farms.size,
      rows: scoped.length,
      acres: scoped.reduce((s, f) => s + f.acres, 0),
      pp: scoped.reduce((s, f) => s + f.ppAcres, 0),
      liability: scoped.reduce((s, f) => s + f.liability, 0),
      premium: scoped.reduce((s, f) => s + f.premium + f.scoPremium + f.ecoPremium, 0),
    };
  }, [scoped]);

  // ── The election board: for every option and both overlays, how much of the
  // CURRENT scope has it. Each number is a filter shortcut, so "80 have TA / 23
  // don't" is one click from the list of the 23.
  // Every option code present in the year's data, known ones first in reading
  // order, then anything else the file carries (EI/QB/PR/ST/EO today) sorted by
  // how many coverages hold it. Hardcoding the list would silently hide a code
  // the AIP started sending — the same "options come from the data" rule the
  // facet rows follow.
  const optCodes = useMemo(() => {
    const seen = new Map();
    facts.forEach(f => f.codes.forEach(c => seen.set(c, (seen.get(c) || 0) + 1)));
    CBS_OPT_ORDER.forEach(c => { if (!seen.has(c)) seen.set(c, 0); });
    const extra = [...seen.keys()].filter(c => !CBS_OPT_ORDER.includes(c))
      .sort((a, b) => seen.get(b) - seen.get(a) || a.localeCompare(b));
    return [...CBS_OPT_ORDER, ...extra];
  }, [facts]);

  // ── FARM-LEVEL, AND MUTUALLY EXCLUSIVE ──────────────────────────────────
  // "Has it" and "doesn't" were counted per COVERAGE and then deduplicated to
  // farms, so a farm carrying ECO in two counties and not a third landed in
  // BOTH columns — and clicking "doesn't" put it on a call list for a product
  // it already owns on that crop.
  //
  // Michael J Meisenzahl's CY2026 wheat is the case Kevin caught: ECO and SCO
  // in Monroe and Ontario, neither in Livingston, where he also planted
  // nothing. Read per coverage that is "no ECO"; read the way an agent works —
  // per farm, per crop — he plainly has ECO on wheat.
  //
  // So a farm HAS an election on a crop when ANY of its coverages carries it,
  // and lacks it only when NONE do. The two columns are now a partition of the
  // farms in scope, which is also what makes the row totals add up.
  //
  // Note this is NOT the same as saying the AIP data was wrong: the file really
  // does carry no ECO row for that Livingston coverage, and zero acres is not
  // the cause — his Ontario wheat has zero acres and carries ECO. What changed
  // is the QUESTION the number answers.
  const board = useMemo(() => {
    const byFarm = cbsByFarm(scoped);
    // Sets, not counts — the drill-down opens the same membership the number
    // was computed from, so a headcount and its name list can never disagree.
    const split = (pred) => {
      const has = new Set(), no = new Set();
      byFarm.forEach((fs, id) => (cbsFarmHas(fs, pred) ? has : no).add(id));
      return { has, no };
    };
    // Three-state, resolved at the FARM: any coverage electing it wins; failing
    // that, any coverage the feed reached and that did NOT elect it means a real
    // "no"; only when neither is true is the farm genuinely uncaptured.
    const optSplit = (code) => {
      const has = new Set(), no = new Set(), unknown = new Set();
      byFarm.forEach((fs, id) => {
        const st = cbsFarmOptState(fs, code);
        (st === CBS_HAS ? has : st === CBS_NO ? no : unknown).add(id);
      });
      return { has, no, unknown };
    };
    const acresWhere = (pred) => scoped.filter(pred).reduce((s, f) => s + f.acres, 0);
    const rowsFor = (code) => {
      const s = optSplit(code);
      return { code, name: INSURANCE_OPTION_NAME[code] || null, ...s,
               acres: acresWhere(f => cbsOptState(f, code) === CBS_HAS) };
    };
    return {
      options: optCodes.map(rowsFor),
      sco: { ...split(f => f.sco), acres: acresWhere(f => f.sco) },
      eco: { ...split(f => f.eco), acres: acresWhere(f => f.eco) },
      units: [...new Set(scoped.map(f => f.unitStructure || "Not reported"))].sort().map(u => ({
        u, farms: new Set(scoped.filter(f => (f.unitStructure || "Not reported") === u).map(f => f.farmerId)).size,
        acres: acresWhere(f => (f.unitStructure || "Not reported") === u),
      })),
    };
  }, [scoped, optCodes]);

  // WHO is behind a number. Collapses the scoped facts to one line per farm so a
  // grower with the same election in three counties is one name, not three, and
  // carries enough with it (counties, acres, what they hold) to act on without
  // leaving the page.
  //
  // THE PANEL STORES A PREDICATE, NOT ROWS. It used to snapshot the rows at click
  // time, which meant it stopped agreeing with the filters the moment you touched
  // one: open "TA — doesn't have it", then add the WHEAT chip, and the panel still
  // listed 191 farms and 69,480 acres of corn, soybeans and grapes under a wheat
  // filter. A drill-down that disagrees with the filter bar is worse than none —
  // it is the same "two numbers on one card" defect as the ARC rate-vs-cheque
  // banner, and an agent has no way to tell which half is stale. Deriving the rows
  // from `scoped` on every render makes the disagreement unrepresentable.
  const drillInto = useCallback((label, pred) => setDrill({ label, pred }), []);

  const drillRows = useMemo(() => {
    if (!drill) return [];
    const m = new Map();
    scoped.filter(drill.pred).forEach(f => {
      const cur = m.get(f.farmerId) || { farm: f.farm, policies: new Set(), counties: new Set(), crops: new Set(), codes: new Set(), acres: 0 };
      cur.policies.add(f.policyNumber); cur.counties.add(f.county); cur.crops.add(f.crop);
      f.codes.forEach(c => cur.codes.add(c));
      cur.acres += f.acres;
      m.set(f.farmerId, cur);
    });
    return [...m.values()]
      .map(r => ({ ...r, policies: [...r.policies].sort(), counties: [...r.counties].sort(), crops: [...r.crops].sort(), codes: [...r.codes].sort() }))
      .sort((a, b) => b.acres - a.acres || a.farm.localeCompare(b.farm));
  }, [drill, scoped]);

  // ── Rollup on any dimension. Top-N with the tail folded into one Other row —
  // a silently dropped tail reads as "this is everything" when it isn't.
  const grouped = useMemo(() => {
    const g = CBS_DIMS.find(d => d.key === groupBy).get;
    const m = new Map();
    scoped.forEach(f => {
      const k = g(f) || "—";
      const cur = m.get(k) || { key: k, farms: new Set(), rows: 0, acres: 0, liability: 0, premium: 0, sco: new Set(), eco: new Set(), ta: new Set() };
      cur.farms.add(f.farmerId); cur.rows += 1; cur.acres += f.acres;
      cur.liability += f.liability; cur.premium += f.premium + f.scoPremium + f.ecoPremium;
      if (f.sco) cur.sco.add(f.farmerId);
      if (f.eco) cur.eco.add(f.farmerId);
      if (cbsOptState(f, "TA") === CBS_HAS) cur.ta.add(f.farmerId);
      m.set(k, cur);
    });
    // Sets are KEPT, not reduced to sizes: cnt() drills on the same membership
    // it counted, so a rollup number and the names behind it cannot disagree.
    return [...m.values()].sort((a, b) => b.acres - a.acres || b.farms.size - a.farms.size);
  }, [scoped, groupBy]);

  // ── Farm list. One row per farm per crop — the grain an agent makes a call on.
  const farmRows = useMemo(() => {
    const m = new Map();
    scoped.forEach(f => {
      const k = f.farmerId + "|" + f.crop;
      const cur = m.get(k) || {
        key: k, farm: f.farm, crop: f.crop, farmerId: f.farmerId,
        counties: new Set(), policies: new Set(), aips: new Set(), agents: new Set(),
        units: new Set(), levels: new Set(), plans: new Set(),
        acres: 0, pp: 0, liability: 0, premium: 0,
        codes: new Set(), captured: false, sco: false, eco: false, ecoBand: null, scoBand: null,
      };
      cur.counties.add(f.county); cur.policies.add(f.policyNumber); cur.aips.add(f.aip);
      cur.agents.add(f.agent); cur.plans.add(f.plan);
      cur.units.add(f.unitStructure || "—");
      if (f.level != null) cur.levels.add(f.level);
      cur.acres += f.acres; cur.pp += f.ppAcres; cur.liability += f.liability;
      cur.premium += f.premium + f.scoPremium + f.ecoPremium;
      f.codes.forEach(c => cur.codes.add(c));
      cur.captured = cur.captured || f.optionsCaptured;
      cur.sco = cur.sco || f.sco; cur.eco = cur.eco || f.eco;
      if (f.ecoBand != null) cur.ecoBand = Math.max(cur.ecoBand ?? 0, f.ecoBand);
      if (f.scoBand != null) cur.scoBand = Math.max(cur.scoBand ?? 0, f.scoBand);
      m.set(k, cur);
    });
    const arr = [...m.values()].map(r => ({
      ...r,
      counties: [...r.counties].sort(), policies: [...r.policies].sort(),
      aips: [...r.aips].sort(), agents: [...r.agents].sort(),
      units: [...r.units].filter(u => u !== "—").sort(),
      levels: [...r.levels].sort((a, b) => b - a),
      plans: [...r.plans].sort(), codes: [...r.codes].sort(),
    }));
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (r) => ({ farm: r.farm, crop: r.crop, acres: r.acres, liability: r.liability, premium: r.premium,
                          units: r.units.join(","), level: r.levels[0] ?? -1 }[sortCol]);
    return arr.sort((a, b) => {
      const x = val(a), y = val(b);
      if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
      return String(x).localeCompare(String(y)) * dir;
    });
  }, [scoped, sortCol, sortDir]);

  const exportCsv = () => {
    const head = ["Farm", "Crop", "Counties", "Policies", "Carrier", "Agent", "Plan", "Units", "Coverage level",
                  "Insured acres", "PP acres", "Liability", "Producer premium", "Options", "Options captured", "SCO", "ECO"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = farmRows.map(r => [
      r.farm, r.crop, r.counties.join(" / "), r.policies.join(" / "), r.aips.join(" / "), r.agents.join(" / "),
      r.plans.join(" / "), r.units.join(" / "), r.levels.map(l => l + "%").join(" / "),
      r.acres.toFixed(1), r.pp.toFixed(1), Math.round(r.liability), Math.round(r.premium),
      r.codes.join(" "), r.captured ? "yes" : "no",
      r.sco ? (r.scoBand ? `yes ${r.scoBand}%` : "yes") : "no",
      r.eco ? (r.ecoBand ? `yes ${r.ecoBand}%` : "yes") : "no",
    ].map(esc).join(","));
    const blob = new Blob([[head.map(esc).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `coverage-book-scan-CY${year}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ── styles, matched to the surrounding palette
  const card = { background: "#fff", border: "1px solid #e2ebe2", borderRadius: 12, padding: 16, marginBottom: 14 };
  const chipBtn = (on) => ({ padding: "5px 11px", borderRadius: 999, border: "1px solid " + (on ? "#2d6a2d" : "#dde7dd"),
    background: on ? "#2d6a2d" : "#fff", color: on ? "#fff" : "#4a6e4a", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" });
  const triChip = (state) => {
    const skin = state === CBS_HAS ? { bg: "#2d6a2d", bd: "#2d6a2d", fg: "#fff" }
      : state === CBS_NO ? { bg: "#b3462f", bd: "#b3462f", fg: "#fff" }
      : state === CBS_UNCAPTURED ? { bg: "#7a6a3a", bd: "#7a6a3a", fg: "#fff" }
      : { bg: "#fff", bd: "#dde7dd", fg: "#4a6e4a" };
    return { padding: "5px 11px", borderRadius: 999, border: `1px solid ${skin.bd}`, background: skin.bg, color: skin.fg,
             fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
  };
  const lbl = { fontSize: 10, fontWeight: 800, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 };
  const th = { textAlign: "left", padding: "7px 8px", fontSize: 10.5, fontWeight: 800, color: "#4a6e4a",
               textTransform: "uppercase", letterSpacing: ".05em", borderBottom: "1px solid #e2ebe2", whiteSpace: "nowrap" };
  const td = { padding: "7px 8px", fontSize: 12.5, color: "#1a2e1a", borderBottom: "1px solid #f0f5f0", verticalAlign: "top" };
  const num = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

  const kpi = (label, value, sub) => (
    <div key={label} style={{ background: "#fff", border: "1px solid #e2ebe2", borderRadius: 12, padding: "12px 16px", minWidth: 132, flex: "1 1 132px" }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
      <div style={{ fontSize: 21, fontWeight: 800, color: "#1a2e1a", marginTop: 3 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: "#60735f", marginTop: 2 }}>{sub}</div> : null}
    </div>
  );

  // A clickable headcount. Every number on this screen opens the names behind it —
  // that is the difference between a dashboard and something an agent can work
  // from. A zero stays inert: there is nothing to open, and a dead link that
  // looks live is worse than plain text.
  // Takes the SET the number was computed from, not a count plus a separate
  // predicate — those two drifted apart the moment the board went farm-level.
  const cnt = (set, color, label) => (set && set.size
    ? <button onClick={() => drillInto(label, f => set.has(f.farmerId))} title={`Show the ${set.size} farm${set.size === 1 ? "" : "s"}`}
        style={{ background: "none", border: "none", font: "inherit", color, fontWeight: 800, cursor: "pointer",
                 padding: 0, textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>{fmt(set.size)}</button>
    : <span style={{ color: "#b9c6b9", fontWeight: 800 }}>0</span>);

  const sortBtn = (col, label, right) => (
    <th style={{ ...th, textAlign: right ? "right" : "left", cursor: "pointer" }}
        onClick={() => { if (sortCol === col) setSortDir(d => (d === "asc" ? "desc" : "asc")); else { setSortCol(col); setSortDir(col === "farm" || col === "crop" ? "asc" : "desc"); } }}>
      {label}{sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );

  if (loading) return <div style={{ padding: 24, color: "#5c6f5c", fontSize: 13 }}>Reading the book…</div>;
  if (err) return <div style={{ ...card, background: "#fdf0ee", borderColor: "#e8c4bc", color: "#8c3320", fontSize: 13 }}>Couldn’t load: {err}</div>;

  const facetRows = [
    ["Season", "season", seasonSel, setSeasonSel],
    ["Crop", "crop", cropSel, setCropSel],
    ["State", "state", stateSel, setStateSel],
    ["Carrier", "aip", aipSel, setAipSel],
    ["Agent", "agent", agentSel, setAgentSel],
    ["Plan", "plan", planSel, setPlanSel],
    ["Units", "unit", unitSel, setUnitSel],
    ["Level", "level", levelSel, setLevelSel],
    ["Coverage", "covType", covTypeSel, setCovTypeSel],
    ["County", "county", countySel, setCountySel],
  ];

  return (
    <div>
      <div style={{ fontSize: 12.5, color: "#5c6f5c", marginBottom: 12 }}>
        Every election on the CY{year} book, filtered on any combination. Chips are additive across rows and
        alternative within one. Option chips <b>cycle</b>: click once for <i>has it</i>, again for <i>doesn’t</i>,
        again for <i>not captured</i>.
        {" "}<b style={{ color: "#40564a" }}>Straight off the M13/NAU file</b> — the options are the AIP’s own
        P15 field, so “none elected” is the carrier’s answer, not ours. Nothing here is modelled or inferred.
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <span style={lbl}>Crop year</span>
        <select value={year} onChange={e => setYear(+e.target.value)}
          style={{ fontSize: 13, padding: "6px 10px", borderRadius: 7, border: "1px solid #d5e2d5", background: "#fff", fontFamily: "inherit" }}>
          {years.map(y => <option key={y} value={y}>CY{y}</option>)}
        </select>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="farm or policy number…"
          style={{ fontSize: 13, padding: "6px 10px", borderRadius: 7, border: "1px solid #d5e2d5", minWidth: 200, fontFamily: "inherit" }} />
        <button onClick={exportCsv} style={{ ...chipBtn(false), cursor: "pointer" }}>⬇ CSV</button>
        {anyFilter ? <button onClick={resetAll} style={{ ...chipBtn(false), borderStyle: "dashed", color: "#a33", borderColor: "#e6c9c9" }}>reset all filters</button> : null}
      </div>

      {/* Fall CY2026 wheat and spring CY2026 are the same crop year — worth saying,
          because "last year's wheat" and "this year's corn" are one selection here. */}
      <div style={{ ...card, background: "#f4f8f4", borderColor: "#dbe7db", fontSize: 12.5, color: "#40564a", lineHeight: 1.55, padding: "11px 14px" }}>
        <b>Winter wheat sold in autumn {year - 1} IS crop year {year}</b> — so “last year’s wheat” and “this year’s
        spring crops” are the same selection here. Split them with the <b>Season</b> chips.
      </div>

      {!!uncaptured.aips.length && (
        <div style={{ ...card, background: "#fffbe9", borderColor: "#e8dca8", fontSize: 12.5, color: "#6b5a1e", lineHeight: 1.55, padding: "11px 14px" }}>
          <b>{fmt(uncaptured.farms)} farm{uncaptured.farms === 1 ? "" : "s"} ({fmt(Math.round(uncaptured.acres))} ac)
          are not on an M13 feed</b> — the hand-loaded books ({uncaptured.aips.join(", ")}), typed from SOI PDFs
          that never carried the options field. M13 gives us nothing for them, so nothing is what they report:
          those rows answer “<i>not captured</i>”, never “doesn’t have it”. Every option chip has that as its
          third state, so you can see exactly whose elections are unknown before working a call list.
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        {kpi("Farms", fmt(totals.farms), `${fmt(totals.rows)} coverage${totals.rows === 1 ? "" : "s"}`)}
        {kpi("Insured acres", fmt(Math.round(totals.acres)), totals.pp > 0 ? `incl. ${fmt(Math.round(totals.pp))} PP` : null)}
        {kpi("Liability", fmtD0(Math.round(totals.liability)))}
        {kpi("Producer premium", fmtD0(Math.round(totals.premium)), "MPCI + SCO + ECO")}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div style={{ ...card, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span style={lbl}>Options</span>
          {CBS_OPT_ORDER.map(code => (
            <button key={code} onClick={() => cycleOpt(code)} style={triChip(optSel[code] || null)}
              title={`${INSURANCE_OPTION_NAME[code] || code} — click to cycle: has → doesn’t → not captured → any`}>
              {code}{optSel[code] === CBS_HAS ? " ✓" : optSel[code] === CBS_NO ? " ✗" : optSel[code] === CBS_UNCAPTURED ? " ?" : ""}
            </button>
          ))}
          <span style={{ ...lbl, marginLeft: 10 }}>Area</span>
          <button onClick={cycleTri(scoSel, setScoSel)} style={triChip(scoSel)} title="SCO — click to cycle: has → doesn’t → any">
            SCO{scoSel === CBS_HAS ? " ✓" : scoSel === CBS_NO ? " ✗" : ""}
          </button>
          <button onClick={cycleTri(ecoSel, setEcoSel)} style={triChip(ecoSel)} title="ECO — click to cycle: has → doesn’t → any">
            ECO{ecoSel === CBS_HAS ? " ✓" : ecoSel === CBS_NO ? " ✗" : ""}
          </button>
        </div>

        {facetRows.map(([label, dim, sel, setSel]) => {
          const opts = facetOpts(dim);
          if (!opts.length) return null;
          const collapsible = opts.length > 14 && !sel.size && !facetOpen[dim];
          const shown = collapsible ? opts.slice(0, 14) : opts;
          return (
            <div key={dim} style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span style={lbl}>{label}</span>
              {shown.map(o => (
                <button key={o} onClick={() => toggleSet(setSel)(o)} style={chipBtn(sel.has(o))}
                  title={dim === "unit" ? (CBS_UNIT_LABEL[o] || o) : undefined}>{o}</button>
              ))}
              {shown.length < opts.length && (
                <button type="button" onClick={() => setFacetOpen(o => ({ ...o, [dim]: true }))}
                  style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: 11, fontWeight: 700, color: "#2d6a2d", cursor: "pointer" }}>
                  +{opts.length - shown.length} more — show all
                </button>
              )}
              {facetOpen[dim] && opts.length > 14 && (
                <button type="button" onClick={() => setFacetOpen(o => ({ ...o, [dim]: false }))}
                  style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: 11, color: "#677167", cursor: "pointer" }}>show fewer</button>
              )}
              {!!sel.size && <button onClick={() => setSel(new Set())} style={{ ...chipBtn(false), borderStyle: "dashed" }}>clear</button>}
            </div>
          );
        })}
      </div>

      {/* ── Drill-down: the names behind whichever number was clicked ─────── */}
      {drill && (
        <div style={{ ...card, borderColor: "#c9dcc9", background: "#f8fbf8" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "baseline", marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>{drill.label}</div>
            <div style={{ fontSize: 12, color: "#5c6f5c" }}>
              {fmt(drillRows.length)} farm{drillRows.length === 1 ? "" : "s"} ·{" "}
              {fmt(Math.round(drillRows.reduce((s, r) => s + r.acres, 0)))} ac
              {anyFilter ? <span style={{ color: "#677167" }}> · within the current filters</span> : null}
            </div>
            <button onClick={() => setDrill(null)} style={{ ...chipBtn(false), marginLeft: "auto" }}>close</button>
          </div>
          {drillRows.length ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                <thead><tr>
                  <th style={th}>Farm</th><th style={th}>Crop</th><th style={th}>County</th>
                  <th style={th}>Options on policy</th><th style={{ ...th, textAlign: "right" }}>Acres</th>
                </tr></thead>
                <tbody>
                  {drillRows.map((r, i) => (
                    <tr key={r.farm + i}>
                      <td style={td}><b>{r.farm}</b><div style={{ fontSize: 10.5, color: "#677167" }}>{r.policies.join(", ")}</div></td>
                      <td style={{ ...td, fontSize: 11.5 }}>{r.crops.join(", ")}</td>
                      <td style={{ ...td, fontSize: 11.5 }}>{r.counties.join(", ")}</td>
                      <td style={td}>
                        {r.codes.length ? (
                          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                            {r.codes.map(c => (
                              <span key={c} title={INSURANCE_OPTION_NAME[c] || `${c} — meaning not established`}
                                style={{ fontSize: 10.5, fontWeight: 800, color: "#1f5c2e", background: "#e7f3e7",
                                         border: "1px solid #b7d3b7", borderRadius: 5, padding: "1px 6px" }}>{c}</span>
                            ))}
                          </span>
                        ) : <span style={{ fontSize: 11, color: "#677167", fontStyle: "italic" }}>—</span>}
                      </td>
                      <td style={num}>{fmt(Math.round(r.acres))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ fontSize: 12.5, color: "#677167", fontStyle: "italic" }}>
                No farms — the filters have narrowed this cell to nothing since it was opened.
              </div>}
        </div>
      )}

      {/* ── The election board ─────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 3 }}>What this selection has elected</div>
        {/* A farm can elect an option in one county and not another, so the three
            columns are three headcounts and CAN sum past the farm total. Said out
            loud, because a row reading 102 + 32 against 129 farms otherwise looks
            like a double-count. */}
        <div style={{ fontSize: 11.5, color: "#60735f", marginBottom: 12 }}>
          Farms, counted once each — <b>per farm and crop, not per county</b>. A grower who carries ECO in
          two counties and not a third HAS ECO; he is not also a farm that doesn’t. So “has it” and “doesn’t”
          are exclusive and add to the {fmt(totals.farms)} farms in scope. <b>Click any number to see who.</b>
          For the county-by-county gaps, use the rollup below or the list.
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead><tr>
              <th style={th}>Election</th>
              <th style={{ ...th, textAlign: "right" }}>Has it</th>
              <th style={{ ...th, textAlign: "right" }}>Doesn’t</th>
              <th style={{ ...th, textAlign: "right" }}>Not captured</th>
              <th style={{ ...th, textAlign: "right" }}>Acres with it</th>
            </tr></thead>
            <tbody>
              {board.options.map(r => (
                <tr key={r.code}>
                  <td style={td}><b>{r.code}</b>{" "}
                    {r.name ? <span style={{ color: "#60735f" }}>{r.name}</span>
                            : <span style={{ color: "#b8860b", fontStyle: "italic" }} title="This code is in the AIP file but we hold no sourced meaning for it — shown raw rather than guessed.">meaning not established</span>}</td>
                  <td style={num}>{cnt(r.has, "#2d6a2d", `${r.code} — has it`)}</td>
                  <td style={num}>{cnt(r.no, "#b3462f", `${r.code} — doesn’t have it`)}</td>
                  <td style={num}>{cnt(r.unknown, "#7a6a3a", `${r.code} — not captured`)}</td>
                  <td style={num}>{r.acres > 0 ? fmt(Math.round(r.acres)) : "—"}</td>
                </tr>
              ))}
              {[["SCO", board.sco, "Supplemental Coverage Option", f => f.sco], ["ECO", board.eco, "Enhanced Coverage Option", f => f.eco]].map(([name, b, longName, pred]) => (
                <tr key={name} style={{ background: "#f8fbf8" }}>
                  <td style={td}><b>{name}</b> <span style={{ color: "#60735f" }}>{longName}</span></td>
                  <td style={num}>{cnt(b.has, "#2d6a2d", `${name} — has it`)}</td>
                  <td style={num}>{cnt(b.no, "#b3462f", `${name} — doesn’t have it`)}</td>
                  <td style={{ ...num, color: "#b9c6b9" }}>n/a</td>
                  <td style={num}>{b.acres > 0 ? fmt(Math.round(b.acres)) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* SCO/ECO presence is read from the OVERLAY ROW, not from an options
            string, so it has no "not captured" state — the endorsement is either
            on the policy as its own coverage or it isn't, on every carrier. */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f0f5f0", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={lbl}>Unit structure</span>
          {board.units.map(u => (
            <button key={u.u} onClick={() => toggleSet(setUnitSel)(u.u)} style={{ ...chipBtn(unitSel.has(u.u)), fontWeight: 700 }}
              title={CBS_UNIT_LABEL[u.u] || u.u}>
              {u.u} · {fmt(u.farms)} farm{u.farms === 1 ? "" : "s"} · {fmt(Math.round(u.acres))} ac
            </button>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 11.5, color: "#60735f", lineHeight: 1.5 }}>
          <b>Elected, not qualified.</b> This is the unit structure the policy is WRITTEN at. Whether a farm
          <i> qualified</i> for an enterprise unit is a determination the AIP makes against RMA’s section /
          acreage test, and that determination is not in the M13 file — so “who didn’t qualify for EU” can’t be
          answered from here yet. Ask the AIP, or send it as a question and it can be added once there’s a source.
        </div>
      </div>

      {/* ── Rollup ─────────────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginRight: 6 }}>Rollup by</div>
          {CBS_DIMS.filter(d => d.key !== "farm").map(d => (
            <button key={d.key} onClick={() => setGroupBy(d.key)} style={chipBtn(groupBy === d.key)}>{d.label}</button>
          ))}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead><tr>
              <th style={th}>{(CBS_DIMS.find(d => d.key === groupBy) || {}).label}</th>
              <th style={{ ...th, textAlign: "right" }}>Farms</th>
              <th style={{ ...th, textAlign: "right" }}>Acres</th>
              <th style={{ ...th, textAlign: "right" }}>TA</th>
              <th style={{ ...th, textAlign: "right" }}>SCO</th>
              <th style={{ ...th, textAlign: "right" }}>ECO</th>
              <th style={{ ...th, textAlign: "right" }}>Liability</th>
              <th style={{ ...th, textAlign: "right" }}>Premium</th>
            </tr></thead>
            <tbody>
              {grouped.map(r => {
                // Every cell in a rollup row is a headcount too, so each opens the
                // same way: the group's farms, narrowed by that column's election.
                const inGroup = (f) => (CBS_DIMS.find(d => d.key === groupBy).get(f) || "—") === r.key;
                const L = (CBS_DIMS.find(d => d.key === groupBy) || {}).label;
                return (
                  <tr key={r.key}>
                    <td style={td}>
                      <button onClick={() => drillInto(`${L}: ${r.key}`, inGroup)} title={`Show the ${r.farms.size} farm${r.farms.size === 1 ? "" : "s"} in ${r.key}`}
                        style={{ background: "none", border: "none", font: "inherit", fontWeight: 700, color: "#1a2e1a", cursor: "pointer",
                                 padding: 0, textAlign: "left", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>{r.key}</button>
                    </td>
                    <td style={num}>{cnt(r.farms, "#1a2e1a", `${L}: ${r.key}`)}</td>
                    <td style={num}>{fmt(Math.round(r.acres))}</td>
                    <td style={num}>{cnt(r.ta, "#2d6a2d", `${L}: ${r.key} · has TA`)}</td>
                    <td style={num}>{cnt(r.sco, "#2d6a2d", `${L}: ${r.key} · has SCO`)}</td>
                    <td style={num}>{cnt(r.eco, "#2d6a2d", `${L}: ${r.key} · has ECO`)}</td>
                    <td style={num}>{fmtD0(Math.round(r.liability))}</td>
                    <td style={num}>{fmtD0(Math.round(r.premium))}</td>
                  </tr>
                );
              })}
              {!grouped.length && <tr><td colSpan={8} style={{ ...td, color: "#677167", fontStyle: "italic" }}>Nothing matches these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── The list ───────────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline", marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
            {fmt(farmRows.length)} farm × crop {farmRows.length === 1 ? "row" : "rows"}
          </div>
          <div style={{ fontSize: 11.5, color: "#60735f" }}>one line per farm per crop · CSV exports every row, not just the shown ones</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isMobile ? 720 : 900 }}>
            <thead><tr>
              {sortBtn("farm", "Farm")}
              {sortBtn("crop", "Crop")}
              <th style={th}>County</th>
              <th style={th}>Carrier · Agent</th>
              {sortBtn("units", "Units")}
              {sortBtn("level", "Level", true)}
              <th style={th}>Options on policy</th>
              <th style={{ ...th, textAlign: "center" }}>SCO</th>
              <th style={{ ...th, textAlign: "center" }}>ECO</th>
              {sortBtn("acres", "Acres", true)}
              {sortBtn("premium", "Premium", true)}
            </tr></thead>
            <tbody>
              {farmRows.slice(0, rowLimit).map(r => (
                <tr key={r.key}>
                  <td style={td}><b>{r.farm}</b><div style={{ fontSize: 10.5, color: "#677167" }}>{r.policies.join(", ")}</div></td>
                  <td style={td}>{r.crop}</td>
                  <td style={{ ...td, fontSize: 11.5 }}>{r.counties.join(", ")}</td>
                  <td style={{ ...td, fontSize: 11.5 }}>{r.aips.join(", ")}<div style={{ color: "#677167" }}>{r.agents.map(a => RA_AGENT_SHORT[a] || a).join(", ")}</div></td>
                  <td style={td}>{r.units.length ? r.units.join(", ") : <span style={{ color: "#b9c6b9" }}>—</span>}</td>
                  <td style={num}>{r.levels.length ? r.levels.map(l => l + "%").join(", ") : "—"}</td>
                  <td style={td}>
                    {r.codes.length ? (
                      <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                        {r.codes.map(c => (
                          <span key={c} title={INSURANCE_OPTION_NAME[c] || c}
                            style={{ fontSize: 10.5, fontWeight: 800, color: "#1f5c2e", background: "#e7f3e7",
                                     border: "1px solid #b7d3b7", borderRadius: 5, padding: "1px 6px" }}>{c}</span>
                        ))}
                      </span>
                    ) : r.captured
                      ? <span style={{ fontSize: 11, color: "#b3462f", fontWeight: 700 }}>none elected</span>
                      : <span style={{ fontSize: 11, color: "#7a6a3a", fontWeight: 700 }} title="This carrier does not report the options field — unknown, not absent.">not captured</span>}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>{r.sco ? <span style={{ color: "#2d6a2d", fontWeight: 800 }}>✓{r.scoBand ? ` ${r.scoBand}%` : ""}</span> : <span style={{ color: "#d5ded5" }}>—</span>}</td>
                  <td style={{ ...td, textAlign: "center" }}>{r.eco ? <span style={{ color: "#2d6a2d", fontWeight: 800 }}>✓{r.ecoBand ? ` ${r.ecoBand}%` : ""}</span> : <span style={{ color: "#d5ded5" }}>—</span>}</td>
                  <td style={num}>{fmt(Math.round(r.acres))}{r.pp > 0 ? <div style={{ fontSize: 10, color: "#677167" }}>incl. {fmt(Math.round(r.pp))} PP</div> : null}</td>
                  <td style={num}>{fmtD0(Math.round(r.premium))}</td>
                </tr>
              ))}
              {!farmRows.length && <tr><td colSpan={11} style={{ ...td, color: "#677167", fontStyle: "italic" }}>Nothing matches these filters.</td></tr>}
            </tbody>
          </table>
        </div>
        {farmRows.length > rowLimit && (
          <button onClick={() => setRowLimit(n => n + 100)}
            style={{ marginTop: 10, background: "none", border: "none", padding: 0, font: "inherit", fontSize: 12, fontWeight: 700, color: "#2d6a2d", cursor: "pointer" }}>
            show {Math.min(100, farmRows.length - rowLimit)} more of {fmt(farmRows.length)}
          </button>
        )}
      </div>
    </div>
  );
}


// ── FSA-156EZ I/O, handed to the Base Acres panel ───────────────────────────────────────
// The panel owns no fetch of its own: reads go through sbFetch (RLS: admin, or the farmer's own
// portal grant), the PDF through pdfTextItems (isEvalSupported:false), the original through the
// ONE document index, and the save through fsa_156ez_replace (sql/110) — one transaction that
// replaces the farmer-year whole.
const FSA156_API = {
  fetchRows: (table, opts) => sbFetch(table, opts),
  readPdfPages: (file) => pdfTextItems(file),
  // CCC-866: text lines + the drawn check boxes + the form-field dates (src/ccc866Pdf.js), on the app's one pdf.js
  readCccPages: async (file) => readCcc866Pages(await getPDFJS(), new Uint8Array(await file.arrayBuffer())),
  // the customer's producer name on a CCC-866 is matched with the app's ONE name rule
  normName: normFarmerName,
  nameKeys: farmerNameKeys,
  storeOriginal: (orig) => storeImportOriginal(orig),
  signedUrl: (path) => sbStorageSignedUrl("farmer-documents", path),
  logoSrc: async () => (await loadLogoDataUri()) || `${window.location.origin}/Clover_Ag_Logo1_transparent.png`,
  rpc: async (fn, body) => {
    const res = await authedFetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}),
    });
    if (!res.ok) throw new Error(`Supabase ${res.status}: ${(await res.text().catch(() => "")).slice(0, 300)}`);
    return res.json();
  },
};
// The counties where a farmer's BASE ACRES physically sit, for one program year, from their
// stored FSA-156EZ — the ARC/PLC engine's one FSA seam (countyBasisOf). No 156EZ for that
// year (or sql/110 not applied) → null, and the summary stays an insurance-county scenario.
async function fsaBaseAcreCounties(farmerId, programYear) {
  if (!farmerId || programYear == null) return null;
  try {
    const f = [`farmer_id=eq.${farmerId}`, `crop_year=eq.${programYear}`];
    const [tracts, base] = await Promise.all([sbFetch("fsa_tract_records", { filters: f, fresh: true }), sbFetch("fsa_base_acres", { filters: f, fresh: true })]);
    if (!(base || []).length) return null;
    const out = FSA156.baseAcreCountiesOf(tracts, base);
    return Object.keys(out).length ? out : null;
  } catch { return null; }
}

// ── FARM PROGRAMS — the FSA farm-program side of advising, on one page ─────────────────────
// ARC/PLC elections, ARC-IC, base acres (FSA-156EZ) and CCC-866, gathered from where they
// grew up (the Coverage Advisor's Area Coverage Tracker and its Base Acres tab). Nothing is
// re-implemented here: Elections and ARC-IC are CoverageAdvisorView in its `programsTab` mode,
// so the ONE ARC/PLC program-year context still has exactly two builders; Base acres and
// CCC-866 are FsaBaseAcresPanel split by `part`. No ARC/PLC payment dollars print on this page
// (Kevin, 2026-11-01); prices do. Agent app only: it is not in PORTAL_PAGES.
const FARM_PROGRAMS_TABS = [
  { k: "elections", label: "Elections" },
  { k: "arcic",     label: "ARC-IC" },
  { k: "base",      label: "Base acres" },
  { k: "ccc866",    label: "CCC-866" },
];
// How another page opens Farm Programs on a tab, and on a farmer: read once when the page mounts.
function farmProgramsHandoff(tab, farmerId) {
  try {
    if (tab) sessionStorage.setItem("cloverag_fp_tab", tab);
    if (farmerId) sessionStorage.setItem("cloverag_fp_farm", String(farmerId)); else sessionStorage.removeItem("cloverag_fp_farm");
  } catch { /* storage off: the page opens on its default tab */ }
}
const fpTabStyle = (on) => ({ cursor: "pointer", font: "inherit", padding: "7px 14px", borderRadius: 999,
  border: `1px solid ${on ? "#2d4a2d" : T.line}`, background: on ? "#2d4a2d" : "#fff",
  color: on ? "#fff" : T.body, fontSize: 12.5, fontWeight: on ? 800 : 600 });

// FSA's election windows, as ELECTION_WINDOWS carries them (src/strategy/arcPlcSheet.js) — the
// same table the program-year context reads. Program years come from that table, never typed.
function FarmProgramsWindows({ isMobile = false }) {
  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const dt = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const days = (d) => Math.round((new Date(d + "T12:00:00") - new Date(today + "T12:00:00")) / 86400000);
  const rows = Object.keys(ELECTION_WINDOWS).map(Number).sort((a, b) => a - b)
    .map((y) => ({ y, ...ELECTION_WINDOWS[y] }))
    .filter((w) => w.opens && w.closes && w.closes >= today);
  if (!rows.length) return <div style={{ ...uiCard(isMobile ? 12 : 14), marginBottom: 14, fontSize: 13, color: T.muted }}>No ARC/PLC election period is open or announced.</div>;
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
      {rows.map((w) => {
        const open = w.opens <= today, left = days(w.closes);
        return (
          <div key={w.y} style={{ ...uiCard(isMobile ? 12 : 14), flex: "1 1 220px" }}>
            <div style={uiLabel}>{w.y} ARC/PLC election</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: open ? T.green : T.ink, marginTop: 3 }}>
              {open ? `Open · ${left} day${left === 1 ? "" : "s"} left` : `Opens ${dt(w.opens)}`}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{dt(w.opens)} to {dt(w.closes)}</div>
          </div>
        );
      })}
    </div>
  );
}

function FarmProgramsView({ farmers = [], isMobile = false }) {
  const [tab, setTabState] = useState(() => {
    let t = null;
    try { t = sessionStorage.getItem("cloverag_fp_tab"); } catch { /* storage off */ }
    return FARM_PROGRAMS_TABS.some((x) => x.k === t) ? t : "elections";
  });
  const setTab = (k) => { setTabState(k); try { sessionStorage.setItem("cloverag_fp_tab", k); } catch { /* storage off */ } };
  // The farmer Base acres and CCC-866 open on: handed over by Coverage Advisor, or the last one picked here.
  const [farmId, setFarmId] = useState(() => {
    try { const v = sessionStorage.getItem("cloverag_fp_farm"); sessionStorage.removeItem("cloverag_fp_farm"); return v || null; }
    catch { return null; }
  });
  const openTab = (k, fid) => { if (fid) setFarmId(fid); setTab(k); };
  const fsaUi = useMemo(() => ({ T, uiCard, uiLabel, uiNum, UiSpinner }), []);
  const onFsa = tab === "base" || tab === "ccc866";
  return (
    <div style={{ maxWidth: 1240, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <h1 style={{ margin: 0, color: T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>Farm Programs</h1>
        <div style={{ fontSize: 12, color: "#60735f" }}>ARC/PLC elections, ARC-IC, FSA base acres and CCC-866</div>
      </div>
      <div role="tablist" aria-label="Farm Programs" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {FARM_PROGRAMS_TABS.map((t) => (
          <button key={t.k} role="tab" aria-selected={t.k === tab} onClick={() => setTab(t.k)} style={fpTabStyle(t.k === tab)}>{t.label}</button>
        ))}
      </div>
      {tab === "elections" && <FarmProgramsWindows isMobile={isMobile} />}
      {!onFsa && <CoverageAdvisorView farmers={farmers} isMobile={isMobile} programsTab={tab} onOpenFarmPrograms={openTab} />}
      {onFsa && <FsaBaseAcresPanel farmers={farmers} initialFarmerId={farmId} isMobile={isMobile} part={tab} onFarmerChange={setFarmId}
                   api={FSA156_API} ui={fsaUi} />}
    </div>
  );
}

// `programsTab` embeds this view in Farm Programs ("elections" | "arcic"): the ARC/PLC
// election half of the Area Coverage Tracker with no payment dollars, or the ARC-IC screener,
// on this view's ONE program-year context. `onOpenFarmPrograms(tab, farmerId)` is how the
// tracker's Base acres and ARC-IC entry points reach that page.
function CoverageAdvisorView({ farmers = [], isMobile = false, initialView = null, programsTab = null, onOpenFarmPrograms = null }) {
  const [crop, setCrop]     = useState("CORN");
  const [stateName, setStateName] = useState("NY");
  const [countyName, setCountyName] = useState(null);
  const [ref, setRef]       = useState({});   // ref[year][crop]
  const [cty, setCty]       = useState({});   // cty[year][crop][county]
  const [se, setSe]         = useState({});   // se[year][crop][county]
  const [ps, setPs]         = useState({});   // ps[crop] = 2026 RMA projected price
  const [fut, setFut]       = useState([]);   // futures_settles_daily (grain marketing feed) — forward curve
  const [bars, setBars]     = useState({});   // futures_contract_history keyed by contract_sym — the discovery tracker's bars
  const [mi, setMi]         = useState({});   // mi[year][COUNTY] = MI weather-model forecast yield (corn, NY)
  const [ph, setPh]         = useState([]);   // production_history for the selected crop (per-farm CV)
  // The APPROVED APH per farm/county/practice, from crop_coverage. The panel used
  // to average the farm's own yearly yields and call the result "APH"; see the
  // note on farmStats below for why that had to change.
  const [covAph, setCovAph] = useState([]);
  const [view, setView]     = useState(() => {
    if (programsTab) return "county";
    if (initialView) return initialView;
    try { return sessionStorage.getItem("cloverag_coverage_view") || "farm"; } catch { return "farm"; }
  });
  useEffect(() => { if (!programsTab) { try { sessionStorage.setItem("cloverag_coverage_view", view); } catch {} } }, [view, programsTab]);     // "farm" (quoter, default) | "renewals" (per-crop customer list) | "county" (market view)
  // Base Acres moved to Farm Programs (2026-11-02). A browser that still holds "base" as its
  // last tab lands there instead of on an empty tab.
  useEffect(() => {
    // Save "farm" BEFORE leaving: the hop to Farm Programs unmounts this view before the
    // persist effect above can store the new tab, which left "base" stored and bounced
    // every later visit in this browser tab straight back to Farm Programs.
    if (view === "base" && !programsTab) {
      try { sessionStorage.setItem("cloverag_coverage_view", "farm"); } catch {}
      setView("farm"); if (onOpenFarmPrograms) onOpenFarmPrograms("base", null);
    }
  }, [view, programsTab, onOpenFarmPrograms]);
  // Batch-print selection, held here so BOTH the Quoter's own farm picker and the
  // Renewals list can fill it — one selection, one printer.
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchSel, setBatchSel]   = useState({});    // farm name → { name, allPolicyIds }
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState(null);
  // (the farm's FSA PLC payment yield now lives in `payYldBy`, keyed per farm — see below)
  // Kevin, 2026-09-17: "Allow us to enter if the farm is irrigated or not if that
  // matters." It matters in the counties FSA splits, and FSA BLENDS the two rows by
  // the farm's irrigated share rather than choosing one (7 CFR 1412.53(c)) — so the
  // control offers 0 and 100 as one-click answers and a box for a genuinely mixed
  // farm. NULL means unanswered, and an unanswered split county states no figure.
  //
  // ⚠ KEYED PER CROP|STATE|COUNTY, NOT HELD AS ONE PAGE-WIDE ANSWER. A single value
  // FOLLOWED the county picker: measured on the rendered page 2026-09-17, answering
  // 40% irrigated for Van Buren MI corn and then switching to Kalamazoo left
  // Kalamazoo blended at 40% — $924.41 of ARC revenue nobody had answered for — with
  // the "not answered" notice gone, so nothing on screen said the figure was
  // inherited from a different county. The percentage belongs to a farm in ONE
  // county; keying it means a new county starts unanswered and returning to an
  // answered one restores what was said about it.
  //
  // ⚠ AND KEYED PER FARM AS WELL (BUG-092). Keying by the cell alone stopped an
  // answer following the COUNTY picker and left it following the FARM: §1412.53(c)
  // weights the blend by THE FARM's own irrigated percentage, so two growers in one
  // county hold two different true answers, and one grower's answer rated the next
  // grower's figures with the "not answered" notice gone. `irrigationAnswerKey` is
  // the rule and it fails closed — no farm identified means no key, which stores
  // nothing, reads nothing, and leaves the cell UNKNOWN.
  const [irrPctBy, setIrrPctBy] = useState({});
  // WHOSE percentage is being answered. Null until an agent says, because there is
  // no farm selected on a county page and guessing one is what this fixes.
  const [irrFarmId, setIrrFarmId] = useState(null);
  // The same question for the payment yield, which is the same class of fact: an
  // FSA determination belonging to ONE farm, held page-wide, reused across farms and
  // counties. It rides the identical key so the two can never disagree about whose
  // figures are on screen.
  const [payYldBy, setPayYldBy] = useState({});

  /* ⚠ THE COUNTY READ IS SCOPED TO THE STATES THE BOOK ACTUALLY FARMS.
   *
   * Measured 2026-10-06: the unscoped read pulls **18,478 rows to use 1,409** —
   * 92.4% fetched and never looked at — across **19 Range pages**. The comment on
   * that fetch already records the crop half of this ("on 2026-09-17 it went to six
   * crops in 48 states"); the STATE half was never done, and 48 states is where the
   * rows are.
   *
   * ⚠ AND THE PAGING WAS UNORDERED, WHICH IS THE HALF THAT CAN LOSE A COUNTY.
   * `sbFetch` pages by Range and applies no default order, and the weekly FSA ingest
   * UPDATEs this table — an updated row moves in the heap, so a 19-page read spanning
   * an upsert can drop and repeat rows. This repo already records that rule for the
   * JD tables ("order by the PRIMARY KEY; Range paging on a non-unique order drops
   * and repeats rows across page edges"). A dropped county renders as "—" on the
   * Coverage Advisor and reads as a county FSA has no data for.
   *
   * DERIVED FROM THE BOOK, NEVER TYPED. The ingest loads 48 states; hardcoding the
   * four this agency writes would blank the page the day a farm is added elsewhere,
   * which is the wrong failure. An empty book leaves the filter OFF rather than
   * sending `state=in.()` — a filter that matches nothing is worse than none.
   *
   * The dep is the sorted JOINED STRING, not the array: `farmers` is a prop with a
   * new identity most renders, and putting it in the dep array is the unstable-dep
   * refetch loop this repo already paid for.
   */
  const bookStates = useMemo(() => {
    const seen = new Set();
    for (const f of farmers || []) {
      for (const p of (f && f.policies) || []) {
        const st = String((p && p.state) || "").trim().toUpperCase();
        if (/^[A-Z]{2}$/.test(st)) seen.add(st);
      }
      const fs2 = String((f && f.state) || "").trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(fs2)) seen.add(fs2);
    }
    return [...seen].sort();
  }, [farmers]);
  const bookStateKey = bookStates.join(",");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true); setErr(null);
        // The discovery contract's own daily bars (CZ26 / SX26 / WU26) — what the price
        // ladder averages to track a harvest price RMA hasn't announced yet. Same feed and
        // same syms the RMA Prices page and the quoter use, so this card can't drift.
        const discSyms = [...new Set(Object.keys(PRICE_DISCOVERY).map(c => discoveryContractSym(c, 2026)).filter(Boolean))];
        const [refRows, ctyRows, seRows, psRows, futRows, miRows, barRows] = await Promise.all([
          sbFetch("arc_plc_reference", { filters:["program_year=in.(2025,2026,2027)"] }),
          // ⚠ SCOPED TO THE CROPS THIS PAGE CAN SELECT, AND THE FILTER IS DERIVED FROM
          // `CA_ORDER` RATHER THAN TYPED. `caFor` is a pure key lookup — nothing
          // enumerates these rows to build a picker — so a crop outside CA_ORDER is
          // fetched and never read. That was harmless while the ingest carried three
          // crops in four states; on 2026-09-17 it went to six crops in 48 states and
          // this one unscoped read went 18,478 rows / 4.50 MB to 31,882 / 7.74 MB on
          // every Coverage Advisor load. Measured, not estimated. Deriving the list
          // means the day barley joins CA_ORDER the fetch follows it on its own.
          sbFetch("arc_co_county", { select:"program_year,crop,state,county,practice,benchmark_yield,benchmark_revenue,guarantee_revenue,actual_yield,actual_revenue,payment_rate,payment_factor",
            filters:["program_year=in.(2025,2026,2027)", `crop=in.(${CA_ORDER.join(",")})`,
                     ...(bookStateKey ? [`state=in.(${bookStateKey})`] : [])],
            // ORDER BY THE PRIMARY KEY. Range paging with no order can drop and repeat
            // rows across page edges when the weekly ingest moves a row in the heap.
            order:"id.asc" }),
          sbFetch("sco_eco_county", { select:"crop_year,crop,state,county,expected_yield,rma_final_yield,projected_price,harvest_price,expected_revenue,actual_revenue,revenue_ratio,sco_payout_pct,eco95_payout_pct,eco90_payout_pct" }),
          sbFetch("price_settings", { select:"crop,price_guarantee", filters:["crop_year=eq.2026"] }),
          sbFetch("futures_settles_daily", { select:"commodity,contract_date,settle_price,settle_date", filters:["contract_date=not.is.null","commodity=in.(corn,soybeans,wheat)","settle_date=gte.2026-01-01"], order:"settle_date.desc,id.asc" }).catch(() => []),
          sbFetch("mi_model_snapshot", { select:"crop_year,payload", filters:["crop_year=in.(2025,2026,2027)"] }).catch(() => []),
          discSyms.length ? sbFetch("futures_contract_history", { select:"contract_sym,bar_date,close",
            filters:[`contract_sym=in.(${discSyms.join(",")})`], order:"bar_date.asc" }).catch(() => []) : Promise.resolve([]),
        ]);
        if (!alive) return;
        const R = {}; refRows.forEach(r => { (R[r.program_year] = R[r.program_year] || {})[r.crop] = r; });
        // ⚠ KEEP EVERY PRACTICE ROW. This used to keep the FIRST row it saw for a
        // county and only replace it for one marked ALL — and FSA publishes NO "ALL"
        // row for a county it splits by irrigation, so a split county silently kept
        // whichever of irrigated/non-irrigated the database happened to return first.
        // Measured 2026-09-17: 176 CY2026 county/crop cells across our six states are
        // split, 13 of them counties this book farms, and Van Buren MI corn reads a
        // $1,068.62 benchmark irrigated against $806.71 non-irrigated — a $261.91
        // spread decided by row order. 7 CFR 1412.53(c) requires a BLEND weighted by
        // the farm's own irrigated percentage, which is why the rows are all kept and
        // resolvePracticeRow decides.
        const C = {}; ctyRows.forEach(r => { const y = C[r.program_year] = C[r.program_year] || {}; const cc = y[r.crop] = y[r.crop] || {}; const k = r.state + "|" + caCountyKey(r.county); (cc[k] = cc[k] || {})[String(r.practice || "ALL").toUpperCase()] = r; });
        const S = {}; seRows.forEach(r => { const y = S[r.crop_year] = S[r.crop_year] || {}; (y[r.crop] = y[r.crop] || {})[r.state + "|" + caCountyKey(r.county)] = r; });
        const P = {}; psRows.forEach(r => { P[r.crop] = r.price_guarantee != null ? Number(r.price_guarantee) : null; });
        // Yield-Prediction-Model per-county read (corn, NY grid): keep the forecast AND its own
        // 5-yr normal so the Advisor can use the this-year-vs-normal SIGNAL (scale-correct), not the
        // raw absolute yield (which sits ~15-25 bu below the RMA area-yield scale).
        const M = {}; (miRows || []).forEach(r => { const arr = r.payload && r.payload.mi_counties; if (Array.isArray(arr)) M[r.crop_year] = { ...(M[r.crop_year] || {}), ...miCountyMap(arr) }; });
        const B = {}; (barRows || []).forEach(r => { (B[r.contract_sym] = B[r.contract_sym] || []).push(r); });
        setRef(R); setCty(C); setSe(S); setPs(P); setFut(futRows || []); setMi(M); setBars(B);
      } catch (e) { if (alive) setErr(String(e && e.message || e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
    // ⚠ bookStateKey IS IN THE DEPS, AND IT IS THE STRING, NOT THE ARRAY.
    // On a cold deep-link `farmers` can still be empty, so the first run reads
    // unscoped — exactly what shipped before, never an empty page — and re-reads
    // scoped the moment the book lands. On the common path (navigating in from
    // another screen) the book is already loaded and there is only the scoped read:
    // 2 pages instead of 19. Putting `farmers` itself here is the unstable-dep
    // refetch loop; the sorted join is stable.
  }, [bookStateKey]);

  // Per-farm APH history for the selected crop (book-wide) → drives the per-farm CV + basis panel.
  // Refetched when the crop changes; lean columns only.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await sbFetch("production_history", {
          // `practice` is load-bearing: without it a farm insuring both sides has one
          // pooled CV built from two different crops. Jeffres farms Wyoming wheat both
          // ways and 2023 came in at 136.7 conventional against 76.0 organic.
          select: "farmer_id,crop_year,county,state,practice,acres,yield_per_acre,excluded_from_avg",
          filters: [`crop=ilike.${crop}`],
        });
        if (alive) setPh(rows || []);
      } catch { if (alive) setPh([]); }
    })();
    return () => { alive = false; };
  }, [crop]);

  // ── THE APPROVED APH, WHICH IS NOT THE AVERAGE OF THE YIELDS ──────────────
  // crop_coverage is keyed on policy_id, so policies carries the farmer. Both
  // reads are lean and scoped to the selected crop. A failure leaves the list
  // empty, and farmStats then falls back to the yield mean it always used - so
  // this cannot blank the panel, only sharpen it.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cv, pol] = await Promise.all([
          sbFetch("crop_coverage", {
            select: "policy_id,county,practice_code,type_code,aph_yield,acres,plan",
            filters: [`crop=ilike.${crop}`],
          }),
          sbFetch("policies", { select: "id,farmer_id,crop_year" }),
        ]);
        const byPol = {};
        for (const p of (pol || [])) byPol[String(p.id)] = p;
        const out = [];
        for (const c of (cv || [])) {
          // Overlays rate nothing and carry no APH database of their own.
          if (/SCO|ECO|ARPI/i.test(c.plan || "")) continue;
          if (c.aph_yield == null) continue;
          const p = byPol[String(c.policy_id)];
          if (!p || !p.farmer_id) continue;
          out.push({ farmer_id: p.farmer_id, crop_year: p.crop_year,
                     county: (c.county || "").toUpperCase(),
                     practice_code: c.practice_code || "003", type_code: c.type_code || null,
                     aph: +c.aph_yield, acres: c.acres != null ? +c.acres : 0 });
        }
        if (alive) setCovAph(out);
      } catch { if (alive) setCovAph([]); }
    })();
    return () => { alive = false; };
  }, [crop]);

  // states that have data for the selected crop (from ARC, which spans the whole book footprint)
  const states = useMemo(() => {
    const set = new Set();
    [((cty[2025] || {})[crop] || {}), ((cty[2026] || {})[crop] || {}), ((se[2025] || {})[crop] || {})].forEach(o =>
      Object.keys(o).forEach(k => set.add(k.split("|")[0])));
    const order = ["NY", "PA", "OH", "MD"];
    return Array.from(set).sort((a, b) => (((order.indexOf(a) + 1) || 99) - ((order.indexOf(b) + 1) || 99)) || a.localeCompare(b));
  }, [cty, se, crop]);
  const stName = states.includes(stateName) ? stateName : (states[0] || "NY");

  // counties in the selected state that have data for the selected crop (Feed A ∪ Feed B), sorted
  const counties = useMemo(() => {
    const set = new Set();
    [((cty[2025] || {})[crop] || {}), ((cty[2026] || {})[crop] || {}), ((se[2025] || {})[crop] || {})].forEach(o =>
      Object.keys(o).forEach(k => { if (k.startsWith(stName + "|")) set.add(k.slice(k.indexOf("|") + 1)); }));
    return Array.from(set).sort();
  }, [cty, se, crop, stName]);

  const county = (countyName && counties.includes(countyName)) ? countyName
               : (counties.includes("LIVINGSTON") ? "LIVINGSTON" : counties[0] || null);

  // The irrigated-percentage answer for THIS crop, state and county. Reading and
  // writing through the key is what stops one county's answer rating another's
  // figures; every call site below is unchanged.
  // The farms this county has production history for — the set an agent can answer
  // FOR. Names come from the RLS-scoped `farmers` prop; the KEY is the farmer's own
  // uuid, never the name (this book holds two Meisenzahls and an entity whose name
  // changed spelling between crop years).
  const irrFarms = useMemo(() => {
    if (!county) return [];
    const nameById = {};
    (farmers || []).forEach(f => { if (f && f.id) nameById[f.id] = f.name || f.farmer_name || null; });
    const seen = {};
    for (const r of (ph || [])) {
      if ((r.county || "").toUpperCase() !== county) continue;
      if (r.state && stName && String(r.state).toUpperCase() !== stName) continue;
      if (!r.farmer_id || seen[r.farmer_id]) continue;
      seen[r.farmer_id] = { fid: r.farmer_id, name: nameById[r.farmer_id] || ("Farm " + String(r.farmer_id).slice(0, 6)) };
    }
    return Object.values(seen).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [ph, farmers, county, stName]);
  // ⚠ A SELECTION ONLY COUNTS WHILE THAT FARM IS IN THE COUNTY ON SCREEN. Switching
  // county or crop therefore starts UNKNOWN instead of carrying a farm that does not
  // farm there — the fail-closed direction, and one click to answer again.
  const irrFarmSel = irrFarms.some(f => f.fid === irrFarmId) ? irrFarmId : null;
  const irrKey = irrigationAnswerKey(irrFarmSel, crop, stName, county);
  const irrPctIn = irrigationAnswerOf(irrPctBy, irrKey);
  const setIrrPctIn = (v) => setIrrPctBy(m => setIrrigationAnswer(m, irrKey, v));
  // Same key, same fail-closed read: with no farm identified the box is empty and
  // the model falls back to its stated default rather than to the last farm's figure.
  const payYldIn = irrigationAnswerOf(payYldBy, irrKey) != null ? irrigationAnswerOf(payYldBy, irrKey) : "";
  const setPayYldIn = (v) => setPayYldBy(m => setIrrigationAnswer(m, irrKey, v === "" ? null : v));

  const meta = CA_CROPS[crop];
  const now = new Date();
  // Projected price goes through the one ladder (ADM feed → verified constant), with the
  // price_settings row only as a last resort — that row is hand-kept and drifts (2026 wheat
  // sat at $5.74 against RMA's announced $5.76).
  //
  // OUTSIDE NEW YORK the ladder is the wrong answer: RMA publishes per state, and the ladder
  // (like the verified constants) carries NY's. Maryland's 2025 soybeans were $10.64 against
  // NY's $10.54. For an out-of-state county, RMA's own area revenue for THAT state gives the
  // state-correct price, so use it rather than quoting New York's at a Maryland grower.
  const impliedProj = (Y) => {
    const row = county ? ((se[Y] || {})[crop] || {})[stName + "|" + county] : null;
    return (row && row.expected_revenue != null && +row.expected_yield > 0)
      ? Math.round((+row.expected_revenue / +row.expected_yield) * 100) / 100 : null;
  };
  const proj26 = (() => {
    const implied = impliedProj(2026);
    const fallback = implied != null ? implied : (ps[crop] != null ? ps[crop] : null);
    if (stName !== "NY") return fallback;
    const r = resolveRmaPrice(crop, 2026, "projected", bars[discoveryContractSym(crop, 2026)] || null);
    return r.price != null ? r.price : fallback;
  })();
  const futAsOf = (fut && fut.length) ? fut[0].settle_date : null;   // latest grain-futures settle date (feed is fetched desc)
  // Our Yield Prediction Model (Market-Intelligence weather model), corn + NY only. Its ABSOLUTE
  // yields run a different scale than RMA area yields, so we take its this-year-vs-normal SIGNAL
  // (forecast ÷ its own 5-yr normal) and apply it to the official RMA/FSA benchmark — a scale-correct
  // live estimate of the actual county yield. Falls back to the benchmark/expected when uncovered.
  const miFor    = (Y) => (crop === "CORN" && stName === "NY" && county && mi[Y]) ? (mi[Y][county] || null) : null;
  const miRatio  = (o) => (o && o.y != null && o.anchor) ? o.y / o.anchor : null;
  const miPct    = (r) => r != null ? Math.round((r - 1) * 1000) / 10 : null;   // model % vs its normal
  const miTag    = (r) => { const p = miPct(r); return <>{badge("Yield Model", "#2d6a2d")}{p != null ? <span style={{ fontSize:10.5, color:"#5c6f5c", marginLeft:3 }}>{(p >= 0 ? "+" : "") + p}% vs norm</span> : null}</>; };

  // ── formatters + tiny helpers ──
  const _f  = n => n == null ? "—" : "$" + Number(n).toFixed(2);
  const _f1 = n => n == null ? "—" : Number(n).toFixed(1) + " bu";
  const badge = (t, bg = "#2d4a2d") => <span style={{ fontSize:10, fontWeight:700, color:"#fff", background:bg, borderRadius:3, padding:"0 4px", marginLeft:4, verticalAlign:"middle" }}>{t}</span>;
  const pend = t => <span style={{ color:"#5c6f5c", fontStyle:"italic" }}>{t}</span>;

  // ── data accessors ──
  const yr = (Y) => {
    const r = (ref[Y] || {})[crop] || {};
    const erp = r.effective_ref_price != null ? +r.effective_ref_price : null;
    const mya = r.mya_price != null ? +r.mya_price : null;
    return {
      erp, mya, final: r.mya_status === "final",
      bench: r.arc_benchmark_price != null ? +r.arc_benchmark_price : null,
      plc: r.plc_payment_rate != null ? +r.plc_payment_rate : (erp != null && mya != null ? Math.max(0, erp - mya) : null),
      stat: r.statutory_ref_price != null ? +r.statutory_ref_price : null,
    };
  };
  // The raw per-practice cell, and the resolution of it. `caFor` keeps its old
  // shape — a single county row — so every existing caller is untouched; in a split
  // county it is NULL until the irrigated percentage is answered, because there is
  // genuinely no single set of county figures for that farm until then.
  const caCell = (Y) => county ? ((cty[Y] || {})[crop] || {})[stName + "|" + county] : null;
  const caRes = (Y) => resolvePracticeRow(caCell(Y), irrPctIn);
  const caFor = (Y) => caRes(Y).row;
  const sd = county ? ((se[2025] || {})[crop] || {})[stName + "|" + county] : null;
  // ARC/PLC data present for a program year? (drives auto-populating the 2027 column
  // the moment FSA publishes the 2027 benchmark/reference files ~Jan 2027)
  const hasAP = (Y) => { const r = (ref[Y] || {})[crop]; return !!(r && r.effective_ref_price != null) || !!caFor(Y); };

  // ── clocks (marketing year for ARC/PLC; harvest-price discovery for SCO/ECO) ──
  const myProg = (Y) => { const s = new Date(Y, meta.myS[0], meta.myS[1]), e = new Date(Y + 1, meta.myE[0], meta.myE[1]); return Math.max(0, Math.min(1, (now - s) / (e - s))); };
  // SCO/ECO price phase: projected-price (spring guarantee) window → guarantee set →
  // harvest-price window → set. Each window is a live daily-average tracker (needs a
  // futures feed for the running number; the window timing is date-driven here).
  const sePhase = (Y) => {
    const pjS = new Date(Y + meta.pjOff, meta.pjS[0], meta.pjS[1]), pjE = new Date(Y + meta.pjOff, meta.pjE[0], meta.pjE[1]);
    const hvS = new Date(Y, meta.pxS[0], meta.pxS[1]), hvE = new Date(Y, meta.pxE[0], meta.pxE[1]);
    if (now < pjS) return { k:"preproj", pct:0 };
    if (now <= pjE) return { k:"proj", pct:(now - pjS) / (pjE - pjS) };
    if (now < hvS) return { k:"guarset", pct:0 };
    if (now <= hvE) return { k:"harv", pct:(now - hvS) / (hvE - hvS) };
    return { k:"set", pct:1 };
  };

  // ── shared cell UI ──
  const KV = (k, v, i) => (
    <div key={i} style={{ display:"flex", justifyContent:"space-between", gap:8, padding:"4px 0", borderTop: i > 0 ? "1px dashed #e7eee7" : "none", fontSize:12.5 }}>
      <span style={{ color:"#5c6f5c" }}>{k}</span>
      <span style={{ fontWeight:600, fontVariantNumeric:"tabular-nums", textAlign:"right" }}>{v}</span>
    </div>
  );
  const Bar = (pct, color) => (
    <div style={{ height:6, borderRadius:3, background:"#e7eee7", overflow:"hidden", margin:"4px 0" }}>
      <div style={{ height:"100%", width:(Math.round(pct * 100)) + "%", background:color, borderRadius:3 }} />
    </div>
  );
  const Cell = ({ chip, chipBg, chipColor, edge, rows, verdict, vBg, vColor, extra }) => (
    <div style={{ background: edge ? "#fcfdfc" : "#fff", border:"1px solid " + (edge ? "#cfd8cf" : "#d5e2d5"), borderRadius:10, padding:"9px 10px", display:"flex", flexDirection:"column", minWidth:172 }}>
      <div style={{ display:"flex", justifyContent:"flex-end" }}>
        <span style={{ fontSize:10.5, fontWeight:700, textTransform:"uppercase", letterSpacing:".4px", padding:"2px 7px", borderRadius:999, background:chipBg, color:chipColor }}>{chip}</span>
      </div>
      {extra}
      {rows.map((r, i) => KV(r[0], r[1], i))}
      {verdict != null && <div style={{ marginTop:"auto", paddingTop:6 }}><div style={{ fontSize:11.5, fontWeight:700, padding:"5px 7px", borderRadius:6, textAlign:"center", background:vBg, color:vColor }}>{verdict}</div></div>}
    </div>
  );
  const YrHead = (Y, sub, kind) => {
    if (![2025, 2026].includes(Y)) return <div key={Y} style={{ textAlign:"center", alignSelf:"end", paddingBottom:3 }}><div style={{ fontSize:13, fontWeight:700 }}>{Y}</div><div style={{ fontSize:10, color:"#677167" }}>{sub}</div></div>;
    let pct, lbl, col;
    /* ⚠ "THE CALENDAR ELAPSED" AND "FSA PUBLISHED THE FINAL PRICE" ARE DIFFERENT FACTS.
     *
     * Kevin, 2026-10-06: *"2025 area coverage ARC/PLC should be final, but I’m also not
     * convinced this data is correct."* The data IS correct — every figure on the 2025
     * Livingston corn card reconciles to FSA’s own file, and USDA’s published MYA
     * ($4.15), ERP ($4.42) and PLC rate ($0.27) all match what we store. What was wrong
     * is this label.
     *
     * `done` was `pct >= 1 || yr(Y).final` — an OR over two unrelated statements — so a
     * marketing year whose calendar had simply run out printed "MYA complete" in settled
     * green, beside an "Est" badge on the price and a "not final" footer under the rate.
     * Three labels, one card, two of them contradicting the third. That is our plumbing
     * showing through: FSA republishes its PLC workbook on no calendar, and the one from
     * 2026-09-11 still carried the P flag when USDA finalised the price on 2026-09-30.
     *
     * ⚠ THE RULE ALREADY EXISTED IN THIS FUNCTION, TWELVE LINES DOWN, ON THE OTHER BAR.
     * The SCO/ECO branch separates "the window closed" from "RMA announced it" and says
     * `window closed · awaiting RMA` for the gap between them, with a comment explaining
     * exactly why. The MYA bar never got it. Same shape, same colours, same wording.
     */
    if (kind === "my") {
      pct = myProg(Y);
      const closed = pct >= 1;        // the Sep–Aug (or Jun–May) window has run out
      const final = !!yr(Y).final;    // FSA’s own file says the MYA is settled
      if (final)       { col = "#1f7a3d"; lbl = "MYA final · " + meta.mySlbl; pct = 1; }
      else if (closed) { col = "#b8860b"; lbl = "MYA " + meta.mySlbl + " closed · awaiting FSA"; pct = 1; }
      else             { col = "#6b7bb0"; lbl = "MYA " + Math.round(pct * 100) + "% · " + meta.mySlbl; }
    }
    else {
      const p = sePhase(Y);
      // "Set" means RMA has ANNOUNCED it, not that the calendar window closed — the bulletin
      // lands a few days after the last discovery day, and until it does we're still tracking.
      const announced = Y !== 2026 || resolveRmaPrice(crop, 2026, "harvest").status === "final";
      if (p.k === "set" && !announced) { col = "#b8860b"; lbl = "window closed · awaiting RMA"; pct = 1; }
      else if (p.k === "set")    { col = "#1f7a3d"; lbl = "harvest price set"; pct = 1; }
      else if (p.k === "harv")   { col = "#b8860b"; lbl = "harvest tracking " + Math.round(p.pct * 100) + "% · " + meta.pxMo; pct = p.pct; }
      else if (p.k === "guarset"){ col = "#6b7bb0"; lbl = "guarantee set · harvest tracks " + meta.pxMo; pct = 0; }
      else if (p.k === "proj")   { col = "#b8860b"; lbl = "guarantee tracking " + Math.round(p.pct * 100) + "% · " + meta.pjMo; pct = p.pct; }
      else                       { col = "#b9c4b9"; lbl = "guarantee sets " + meta.pjMo + " 1"; pct = 0; }
    }
    return (
      <div key={Y} style={{ textAlign:"center", alignSelf:"end" }}>
        <div style={{ fontSize:13, fontWeight:700 }}>{Y}</div>
        <div style={{ fontSize:10, color:"#677167" }}>{sub}</div>
        {Bar(pct, col)}
        <div style={{ fontSize:10, fontWeight:700, color:col }}>{lbl}</div>
      </div>
    );
  };
  const RLabel = (name, band) => <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", paddingRight:4 }}><div style={{ fontWeight:700, fontSize:13 }}>{name}</div><div style={{ fontWeight:500, fontSize:10.5, color:"#5c6f5c" }}>{band}</div></div>;

  // ── PLC cell (itemized-lite: MYA / ERP / Rate) ──
  const plcCell = (Y) => {
    const d = yr(Y);
    const rows = [
      ["MYA (Actual Price)", d.mya != null ? <>{_f(d.mya)}{d.final ? badge("Actual", "#1f7a3d") : badge("Est", "#6b7bb0")}</> : "—"],
      ["Reference Price", d.erp != null ? _f(d.erp) : "—"],
      ["PLC Rate", d.plc != null ? _f(d.plc) + "/bu" : "—"],
    ];
    const pays = d.plc != null && d.plc > 0;
    const future = d.erp != null && d.mya == null;   // reference loaded, MYA not set yet (next program year)
    const verdict = future ? "reference set · MYA pending" : pays ? (Y === 2025 ? "PAYS" : "tracking") + " ~" + _f(d.plc) : "no pay";
    return <Cell chip={Y === 2025 ? "Pays Oct" : "Elect"} chipBg={Y === 2025 ? "#fbf3df" : "#fbeee6"} chipColor={Y === 2025 ? "#b8860b" : "#a5461b"}
      rows={rows} verdict={verdict} vBg={future ? "#fbf3df" : pays ? (Y === 2025 ? "#e5f4ea" : "#fbf3df") : "#f0f2f0"} vColor={future ? "#b8860b" : pays ? (Y === 2025 ? "#1f7a3d" : "#b8860b") : "#8a8f8a"} />;
  };

  // ── ARC-CO cell (fully itemized) ──
  const arcCell = (Y) => {
    const d = yr(Y), a = caFor(Y);
    const paidFinal = a && a.payment_rate != null;
    const realYld = a ? a.actual_yield : null;       // real FSA county yield (null until harvest final)
    const benchYld = a ? a.benchmark_yield : null;
    const aPx = d.mya, aPxFinal = d.final;           // ARC actual price = MYA (est until final)
    // no actual FSA yield yet (2026+) → estimate it by applying our Yield Prediction Model's
    // above/below-normal signal to the ARC benchmark (scale-correct), else hold at the benchmark
    // (yield-neutral, price-only). Real FSA actual yield overrides both once harvested.
    // ⚠ THE FIGURES ARE countyRead's (arcRead) — the read the recommendation panel above and
    // the printed election sheet use — never a second copy of the shortfall arithmetic here
    // (work order E, 2026-09-18). The county yield it rests on is the same one: FSA's published
    // figure, else the Yield Model's signal on the benchmark, else the benchmark itself.
    const wxR = miRatio(miFor(Y));
    const rd = arcRead(Y);
    const usingMI  = realYld == null && rd.usable && rd.yieldBasis === "scenario" && aPx != null;
    const usingExp = realYld == null && rd.usable && rd.yieldBasis === "benchmark" && benchYld != null && aPx != null;
    const aYld = realYld != null ? realYld : (rd.usable && aPx != null ? rd.arcYield : null);
    const aRev = rd.usable && rd.actualRevenue != null ? rd.actualRevenue : (a && a.actual_revenue != null ? +a.actual_revenue : null);
    // ── 12% OF *BENCHMARK* REVENUE. DO NOT "CORRECT" THIS TO 10% ───────────
    // 7 U.S.C. 9017(d)(1)(B), as amended by P.L. 119-21 (OBBBA) sec. 10305(3):
    // the ARC-CO payment rate may not exceed "10 percent of the benchmark
    // revenue" for 2014–2024 and "12 percent of the benchmark revenue" for
    // **2025–2031**. The guarantee moved 86% → 90% in the same section
    // (9017(c)(1), sec. 10305(2)(A)) — hence "Guarantee (90%)" above.
    //
    // THE TRAP, and it is a live one (checked 2026-08-17): **FSA's own 2025
    // ARC/PLC fact sheet still says "Payment rates may not exceed 10% of the
    // ARC-CO benchmark revenue"** — on the same page that correctly prints the
    // 90% guarantee. They updated one sentence and not the other. Two more
    // secondaries disagree in different directions: CRS R48574 says "12.5% of
    // the county revenue guarantee" (that was the HOUSE bill — wrong percentage
    // AND wrong base), and farmdoc records House 12.5% vs Senate 12%, the Senate
    // number being the one enacted. Anyone reaching for a fact sheet or a
    // summary will find a number that is not the law. Go to the statute.
    const cap12 = a && a.benchmark_revenue != null ? arcCapRate(a.benchmark_revenue) : null;
    const estPay = paidFinal ? +a.payment_rate : (rd.usable ? rd.arcRate : null);
    const maxed = paidFinal ? (estPay != null && cap12 != null && estPay >= cap12 - 0.01) : !!(rd.usable && rd.capped);
    // What FSA actually pays: the rate applies to 85% of base acres (7 U.S.C.
    // 9017(b)). Declared HERE, above `rows`, because both the table and the
    // verdict banner quote it — and a const declared below its first use is a
    // temporal-dead-zone throw at render, not a compile error.
    const payAc = paidFinal ? settledArcPerAcre(a) : (rd.usable ? rd.arcPerAcre : null);
    const blue = { color:"#6b7bb0" };
    const rows = [
      ["ARC Yield", a ? _f1(a.benchmark_yield) : pend("feed")],
      ["ARC Price", a ? _f(d.bench) : "—"],
      ["ARC Revenue", a ? _f(a.benchmark_revenue) : "—"],
      ["Guarantee (90%)", a ? <b>{_f(a.guarantee_revenue)}</b> : "—"],
      ["Actual Yield", realYld != null ? _f1(realYld) : (usingMI ? <>{_f1(aYld)}{miTag(wxR)}</> : usingExp ? <>{_f1(benchYld)}{badge("exp", "#8a7bb0")}</> : pend(Y >= 2026 ? "harvest '" + String(Y).slice(2) : "—"))],
      ["Actual Price", aPx != null ? <>{_f(aPx)}{aPxFinal ? badge("Actual", "#1f7a3d") : badge("Est", "#6b7bb0")}</> : "—"],
      ["Actual Revenue", aRev != null ? <b style={aPxFinal ? {} : blue}>{_f(aRev)}</b> : "—"],
      // RATE, not the cheque. FSA pays ARC-CO on 85% of base acres, and the ARC-vs-PLC panel
      // above compares in $/BASE ACRE — so this cell showing $66.88 beside that panel's $57/ac
      // for the same county read as two different answers. Both are here now, labelled, and
      // the PAID figure is the bold one: the rate is an input, the base-acre number is money.
      [paidFinal ? "Payment rate" : "Est. rate", estPay != null
        ? <><span style={{ ...(paidFinal ? {} : blue), fontWeight:600 }}>{(paidFinal ? "" : "~") + _f(estPay)}{maxed ? " MAX" : ""}</span>
            <div style={{ fontSize:10.5, fontWeight:500, color:"#677167" }}>12% cap on {_f(a && a.benchmark_revenue)}</div></>
        : pend(Y >= 2026 ? "after harvest" : "—")],
      [paidFinal ? "Pays" : "Est. pays", payAc != null
        ? <><b style={paidFinal ? {} : blue}>{(paidFinal ? "" : "~") + _f(payAc)}/base ac</b>
            <div style={{ fontSize:10.5, fontWeight:500, color:"#677167" }}>rate × 85% of base acres</div></>
        : pend(Y >= 2026 ? "after harvest" : "—")],
    ];
    // ── THE BANNER QUOTES THE CHEQUE, NOT THE RATE ────────────────────────
    // It used to print estPay with a "/ac" suffix while the row directly above
    // labelled estPay × 0.85 as "/base ac" — two different dollar figures, both
    // suffixed "/ac", six lines apart on one card ($109.19 vs $92.81 on Genesee
    // corn 2025). Kevin read it and asked which one was the payment, which is
    // the only sensible response to a card that says both.
    //
    // The row above was fixed for exactly this once already (see its comment on
    // the $66.88-vs-$57 mismatch) and this line was not updated in the same
    // pass — the same defect one line lower. So: lead with what FSA actually
    // pays (85% of base acres, 7 U.S.C. 9017(b)), and name the rate as the rate.
    let verdict, vBg, vColor;
    if (paidFinal) { verdict = (a.payment_factor >= 1 ? "MAXED — pays " : "pays ") + _f(payAc) + "/base ac · rate " + _f(estPay) + (a.payment_factor >= 1 ? "" : " · " + Math.round(a.payment_factor * 100) + "%"); vBg = estPay > 0 ? "#e5f4ea" : "#f0f2f0"; vColor = estPay > 0 ? "#1f7a3d" : "#8a8f8a"; }
    else if (estPay != null) { verdict = estPay > 0 ? "est ~" + _f(payAc) + "/base ac · rate " + _f(estPay) + (maxed ? " (maxed) " : " ") + (usingMI ? "· yield model + price" : usingExp ? "· price-only" : "· not final") : (usingMI ? "no trigger (modeled)" : usingExp ? "no trigger on price" : "not triggering yet"); vBg = estPay > 0 ? "#fbf3df" : "#f0f2f0"; vColor = estPay > 0 ? "#b8860b" : "#8a8f8a"; }
    else { verdict = "established · actual pending"; vBg = "#fbf3df"; vColor = "#b8860b"; }
    return <Cell chip={Y === 2025 ? "Pays Oct" : "Elect"} chipBg={Y === 2025 ? "#fbf3df" : "#fbeee6"} chipColor={Y === 2025 ? "#b8860b" : "#a5461b"} rows={rows} verdict={verdict} vBg={vBg} vColor={vColor} />;
  };

  // ── SCO/ECO cell (itemized; bw = band width for the $/ac estimate) ──
  const sd26 = county ? ((se[2026] || {})[crop] || {})[stName + "|" + county] : null;   // real 2026 RMA expected (RIRS)
  const exp26y = sd26 ? +sd26.expected_yield : (sd ? +sd.expected_yield : null);
  const exp26rev = (sd26 && sd26.expected_revenue != null) ? +sd26.expected_revenue : (exp26y != null && proj26 != null ? exp26y * proj26 : null);
  const wxR26 = miRatio(miFor(2026));   // our Yield Prediction Model's 2026 vs-normal signal (corn, NY)
  // ── 2026 harvest price: TRACK the discovery window, then take RMA's announced number ──
  // Same three-state ladder the quoter runs, so this card can never disagree with it:
  //   1. RMA announced        → final. NY wheat 2026 = $6.52 (bulletin PM-26-043, 2026-08-03).
  //   2. discovery running    → the running simple average of the ONE discovery contract's
  //      daily settlements across the window (WU26 over July, CZ26/SX26 over October) — RMA's
  //      own method, run on the days we have so far. Carries how far into the window we are,
  //      because "14 of 22 days" is the difference between a number worth quoting and one
  //      that is still moving. Keeps tracking after the window closes while RMA hasn't posted.
  //   3. window not open yet  → the latest settle of that contract, a forward outlook and the
  //      only rung that is OUR math rather than RMA's. Badged as the contract, not as a price.
  // Before this, the card only ever did (3) — so it quoted the Sep contract straight through
  // wheat's July window and past RMA's announcement, and corn would repeat it every November.
  // The harvest-price ladder for ONE crop year. The SCO/ECO grid below reads it for its own crop-year
  // column; the ARC/PLC panel reads it for the program year it is on — one rule, so a panel on one
  // program year can never print another year's harvest price beside its MYA.
  const harvestFor = (Y) => {
    const res = resolveRmaPrice(crop, Y, "harvest", bars[discoveryContractSym(crop, Y)] || null);
    const final = res.status === "final" && res.price != null;
    const est = res.status === "estimate" && res.price != null;
    const futRow = (final || est) ? null : (() => {   // rung 3 — forward outlook only
      const rows = fut.filter(r => r.commodity === meta.commodity && r.contract_date &&
        new Date(r.contract_date).getUTCFullYear() === Y && (new Date(r.contract_date).getUTCMonth() + 1) === meta.ctrMo);
      if (!rows.length) return null;
      rows.sort((a, b) => a.settle_date < b.settle_date ? 1 : -1);
      return { px: +rows[0].settle_price };
    })();
    return { res, final, est, fut: futRow,
             px: (final || est) ? res.price : (futRow ? futRow.px : null),
             tag: final ? "Actual" : est ? meta.pxMo + " avg" : (futRow ? meta.ctrLbl + " fut" : null) };
  };
  const gridHarvest = harvestFor(2026);
  const harvestRes = gridHarvest.res, harvestFinal = gridHarvest.final, harvestEst = gridHarvest.est, harvestFut = gridHarvest.fut;
  const harvestPx = gridHarvest.px, harvestTag = gridHarvest.tag;
  const harvestNote = harvestEst
    ? harvestRes.days + " of " + harvestRes.expectedDays + " days" + (harvestRes.closed ? " · window closed, awaiting RMA" : " · still moving")
    : null;
  // 2026 estimated actual yield = RMA expected × our model's vs-normal signal (corn, NY), else hold
  // at RMA expected (yield-neutral). The revenue ratio then reflects BOTH the modeled yield signal
  // AND the forward harvest price — so the "would trigger" % moves as the county yield forecast
  // moves day to day, not just as price moves.
  const estY26 = (wxR26 != null && exp26y != null) ? exp26y * wxR26 : exp26y;
  const actRev26 = (estY26 != null && harvestPx != null) ? estY26 * harvestPx : null;
  // RP: the county's expected revenue is revalued at the HIGHER of the projected and harvest
  // price, the same harvest price option the farm's own RP guarantee carries, so the trigger is
  // (estimated yield × harvest) ÷ (expected yield × max(projected, harvest)). Dividing by the
  // projected price alone read revenue high whenever harvest closed above projected (2026:
  // corn $4.75 vs $4.62, beans $12.63 vs $11.09, wheat $6.52 vs $5.76) and understated every
  // trigger (Kevin, 2026-11-03). RP is the plan SCO/ECO ride on in this book; RP-HPE would
  // keep the projected price.
  const harvestUp26 = harvestPx != null && proj26 != null && proj26 > 0 && harvestPx > proj26;
  const expRevRP26 = (exp26rev != null && harvestUp26) ? exp26rev * harvestPx / proj26 : exp26rev;
  const r26 = (actRev26 != null && expRevRP26) ? actRev26 / expRevRP26 : null;
  const scenTrig = (bandTop, bandBot) => r26 != null ? Math.max(0, Math.min(1, (bandTop - r26) / (bandTop - bandBot))) * 100 : null;
  // The settled column's prices are NOT read from a price feed — `sco_eco_county` is a view
  // that back-computes them out of RMA's own area revenue (expected_revenue / expected_yield,
  // actual_revenue / rma_final_yield). That reproduces the announced price to the cent, which
  // is exactly why it makes a good CROSS-CHECK against the ladder: when the two disagree, one
  // of them is wrong and we say so rather than badging a derived number "Actual". This is how
  // the wheat constants were caught being wrong in 2023, 2024 AND 2025 (RMA's ADM sided with
  // the area revenue). Tolerance is a cent — the view rounds to 2dp.
  // ONLY meaningful in New York. RMA prices are per STATE and the ladder carries NY's —
  // Maryland's 2025 soybeans are genuinely $10.64/$11.31 against NY's $10.54/$10.35, so
  // running this check outside NY would flag RMA's own correct number as a conflict on
  // every Maryland county. Out of state we show the area figure and say whose it is.
  const seCheck = (implied, kind) => {
    const v = implied != null ? +implied : null;
    if (v == null) return { v: null, agrees: true, led: null };
    if (stName !== "NY") return { v, led: null, agrees: true, offState: true };
    const led = resolveRmaPrice(crop, 2025, kind).price;
    return { v, led, agrees: !(led != null && Math.abs(led - v) > 0.011) };
  };
  const sePx = (implied, kind) => {
    const c = seCheck(implied, kind);
    if (c.v == null) return "—";
    if (c.offState) return <>{_f(c.v)}{badge("Actual", "#1f7a3d")}<span style={{ fontSize:10, color:"#677167", marginLeft:4 }}>{stName}</span></>;
    return c.agrees
      ? <>{_f(c.v)}{badge("Actual", "#1f7a3d")}</>
      : <>{_f(c.v)}{badge("RMA area", "#b8860b")}
          <div style={{ fontSize:10.5, fontWeight:500, color:"#a5461b" }}>book table says {_f(c.led)} — reconcile</div></>;
  };
  const seSettled = (payoutPct, bandTop, bandLbl) => {
    if (!sd) return [["RMA data", pend("RIRS pending")]];
    return [
      ["Expected Yield", _f1(sd.expected_yield)],
      ["Projected Price", sePx(sd.projected_price, "projected")],
      ["Expected Revenue", _f(sd.expected_revenue)],
      [bandLbl, _f(bandTop * (+sd.expected_revenue))],
      ["Actual Yield", _f1(sd.rma_final_yield)],
      ["Harvest Price", sePx(sd.harvest_price, "harvest")],
      ["Actual Revenue", <b>{_f(sd.actual_revenue)}</b>],
      ["% of Coverage", <b>{Math.round(+payoutPct)}%{+payoutPct >= 100 ? " (max)" : ""}</b>],
    ];
  };
  const se26 = (bandTop, bandBot, bandLbl) => {
    const scenPct = scenTrig(bandTop, bandBot);
    return [
      ["Expected Yield", exp26y != null ? _f1(exp26y) : pend("RMA '26")],
      ["Projected Price", proj26 != null ? <>{_f(proj26)}{badge("Set")}</> : "—"],
      ["Expected Revenue", expRevRP26 != null
        ? (harvestUp26
          ? <>{_f(expRevRP26)}{badge("at harvest", "#b8860b")}
              <div style={{ fontSize:10.5, fontWeight:500, color:"#9a8a5a" }}>RP revalues at the higher harvest price · {_f(exp26rev)} at projected</div></>
          : _f(expRevRP26))
        : "—"],
      [bandLbl, expRevRP26 != null ? _f(bandTop * expRevRP26) : "—"],
      ["Actual Yield", estY26 != null ? (wxR26 != null ? <>{_f1(estY26)}{miTag(wxR26)}</> : <>{_f1(estY26)}{badge("exp", "#8a7bb0")}</>) : pend("harvest '26")],
      ["Harvest Price", harvestPx != null
        ? <>{_f(harvestPx)}{badge(harvestTag, harvestFinal ? "#1f7a3d" : harvestEst ? "#b8860b" : "#6b7bb0")}
            {/* The harvest side has no per-state area figure to fall back on until RMA
                publishes, so off-state it IS New York's number — said, not implied. */}
            {stName !== "NY" ? <span style={{ fontSize:10, color:"#a5461b", marginLeft:4 }}>NY basis</span> : null}
            {harvestNote ? <div style={{ fontSize:10.5, fontWeight:500, color:"#9a8a5a" }}>{harvestNote}</div> : null}</>
        : pend("tracks " + meta.pxMo)],
      ["Actual Revenue", actRev26 != null ? <b style={{ color:"#6b7bb0" }}>{_f(actRev26)}</b> : pend("—")],
      ["Would trigger", scenPct != null ? <b style={{ color: scenPct > 0 ? "#b8860b" : "#5c6f5c" }}>{Math.round(scenPct)}%</b> : pend("after harvest")],
    ];
  };
  const scoEcoCell = (Y, payoutPct, bandTop, bandBot, bandLbl) => {
    if (Y === 2026) {
      const sp = scenTrig(bandTop, bandBot);
      // Name BOTH inputs. With the harvest price final and no yield model for this crop, this
      // read "no trigger at final price" — which sounds settled when the yield underneath it is
      // still RMA's EXPECTED yield standing in (badged "exp" two rows above). The county yield
      // that decides this doesn't publish until ~Feb, and it is the input most likely to move.
      const pxSrc = harvestFinal ? "final price" : harvestEst ? meta.pxMo + " tracking avg" : "current futures";
      const yldSrc = wxR26 != null ? "yield model" : (sd26 && sd26.rma_final_yield != null ? "final yield" : "expected yield");
      const src = yldSrc + " + " + pxSrc;
      const v = sp != null ? (sp > 0 ? "~" + Math.round(sp) + "% at " + src : "no trigger at " + src) : "guarantee set · harvest tracks " + meta.pxMo;
      return <Cell chip="Guar set" chipBg="#eaeefb" chipColor="#6b7bb0" rows={se26(bandTop, bandBot, bandLbl)} verdict={v} vBg="#fbf3df" vColor="#b8860b" />;
    }
    const has = !!sd, pays = has && +payoutPct > 0;
    const verdict = has ? (+payoutPct >= 100 ? "maxed · 100% of coverage" : +payoutPct > 0 ? Math.round(+payoutPct) + "% of coverage" : "no trigger (good year)") : "no coverage";
    return <Cell chip="Paid Jun" chipBg="#e5f4ea" chipColor="#1f7a3d" rows={seSettled(payoutPct, bandTop, bandLbl)} verdict={verdict} vBg={pays ? "#e5f4ea" : "#f0f2f0"} vColor={pays ? "#1f7a3d" : "#8a8f8a"} />;
  };

  // ── ONE ARC/PLC PROGRAM-YEAR CONTEXT — every ARC/PLC surface on this page reads `arcYear` ──
  // The recommendation panel, the one-county election sheet and the farm election summary all
  // read it, and the Full Strategy Report resolves the SAME rule from the SAME session pick — so no
  // surface decides a program year of its own (Kevin, 2026-09-19: "No surface gets to
  // independently decide 2026"). The CURRENT year comes from FSA's announced election windows,
  // never from which year happens to have data: once the active election is a year FSA has not
  // published figures for, the context is PENDING and nothing is computed. A previous program
  // year stays selectable, labelled as previous, and is never substituted into the current one.
  const [arcYrPick, setArcYrPick] = useState(() => readStoredYear("arcPlcElection", null));
  const [arcPublished, setArcPublished] = useState(null);
  useEffect(() => {
    let alive = true;
    sbFetch("arc_plc_reference", { select: "program_year" })
      .then(rows => { if (alive) setArcPublished(availableCropYears(rows, "program_year")); })
      .catch(() => { if (alive) setArcPublished([]); });
    return () => { alive = false; };
  }, []);
  const arcCtx = arcPublished
    ? electionYearContext({ published: arcPublished, windows: ELECTION_WINDOWS, storedYear: arcYrPick })
    : { year: null, pending: false, status: "loading", offered: [], why: "loading" };
  // NULL while loading or pending — a read keyed on it finds nothing, rather than another year.
  const arcYear = arcCtx.pending ? null : arcCtx.year;
  const pickArcYear = (v) => { const y = asCropYear(v); setArcYrPick(y); writeStoredYear("arcPlcElection", null, y); };
  // Whether a program year's FSA election is open — off the same context, so the grid's column
  // label cannot call a closed election open.
  const arcElecTag = (Y) => { const o = arcCtx.offered.find(x => x.year === Y);
    return !o ? "" : (o.status === "current" || o.status === "open") ? " · election open" : o.status === "previous" ? " · election closed" : ""; };


  // ── ARC vs PLC (per county) — ONE ENGINE, NO ASSUMED PAYMENT YIELD ─────────
  // Every figure here is countyRead's (src/strategy/arcPlcElection.js) — the call the Full
  // Strategy Report and the one-page election sheet make — so the three cannot price one
  // county three ways (work order E, 2026-09-18). This widget used to carry its own copy of
  // the ARC-CO arithmetic and decide the election at a DEFAULT payment yield — wheat 60 bu,
  // otherwise 72% of the county benchmark — falling back to a hard-coded crop "lean" when
  // that could not decide. None of those was a fact about the farm. Without the farm's FSA
  // payment yield the answer is the BREAKEVEN: PLC pays more above it, ARC-CO below.
  //
  // The county yield ARC-CO is priced on: FSA's published figure once it exists; before
  // that, for NY corn, the Yield Prediction Model's this-year-vs-normal SIGNAL applied to
  // the FSA benchmark (a scenario factor, labelled "Yield Model" wherever it prints); else
  // the benchmark itself — a normal year. The printed sheet is handed the SAME factor.
  // silasYieldFactor is the ONE rule — the Farm Election Summary resolves its Silas county-yield
  // scenario through the same call, so this page's headline and that page's strip cannot differ.
  const arcYieldFactor = (Y) => silasYieldFactor({ crop, state: stName, countyRow: caFor(Y),
    mi: (county && mi[Y]) ? (mi[Y][county] || null) : null });
  const arcRead = (Y, paymentYield = null) =>
    countyRead({ countyRow: caFor(Y), ref: (ref[Y] || {})[crop] || null, yieldFactor: arcYieldFactor(Y), paymentYield });
  const arcPlcRec = () => {
    const elYr = arcYear;                                // the ONE ARC/PLC program-year context
    if (elYr == null) return null;                       // loading, or PENDING — never another year's figures
    const a = caFor(elYr); if (!a) return null;
    const d = yr(elYr);
    // The farm's OWN payment yield, typed or dragged — never defaulted.
    const ov = payYldIn !== "" && +payYldIn > 0 ? +payYldIn : null;
    const head = arcRead(elYr, ov);
    // Which to enroll in: electionOutlook's switch yield — the SAME call, on the same county row,
    // that the Full Strategy Report and the election sheet print (#208). The decision scale
    // below is the other question: which pays more at USDA's forecast.
    const enrol = enrolRead({ countyRow: a, ref: (ref[elYr] || {})[crop] || null, paymentYield: ov });
    if (!head.usable) return null;
    // Breakeven-vs-price band, holding the county yield where the read above put it:
    // countyRead at each price, same factor, so the band and the headline are one model.
    const beCurve = d.mya != null
      ? [0.2, 0, -0.5, -1.0].map(off => { const m = Math.round((d.mya + off) * 100) / 100;
          // No price floor here: countyRead applies FSA's national loan rate itself (it used to be
          // wheat's $3.72 typed into this line, for every crop).
          const r = (m > 0 && d.erp != null && m < d.erp - 0.12)
            ? countyRead({ countyRow: a, ref: (ref[elYr] || {})[crop] || null, price: m, yieldFactor: arcYieldFactor(elYr) }) : null;
          return { mya: m, be: r && r.usable ? r.breakeven : null }; })
          .filter(p => p.be != null && isFinite(p.be))
      : [];
    // CY2025 for reference: FSA's SETTLED rate where it has published one, else the read.
    const pY = priorCropYear(elYr);                      // the PRIOR program year, shown for reference
    const a25 = caFor(pY), r25 = arcRead(pY, ov), paid25 = settledArcPerAcre(a25);
    const arc25 = paid25 != null ? paid25 : (r25.usable ? r25.arcPerAcre : null);
    // The SAME per-county cell the farm election summary prints — one computation, two surfaces.
    const cell = countyCell({ crop, state: stName, county, practiceRows: caCell(elYr), irrigatedPct: irrPctIn,
      ref: (ref[elYr] || {})[crop] || null, paymentYield: ov, silasFactor: arcYieldFactor(elYr) });
    return { year: elYr, backYear: pY, cell, hv: harvestFor(elYr), proxyYld: a.benchmark_yield != null ? +a.benchmark_yield : null,
             plcRate: head.plcRate, arcAc: head.arcPerAcre, breakeven: head.breakeven, state: head.state,
             label: electionLabel(head), pick: head.pick, effYld: ov, plcAc: head.plcPerAcre, noYield: ov == null,
             enrol, enrolText: enrolLabel(enrol),
             yieldBasis: head.yieldBasis, beCurve,
             erp: d.erp, mya: d.mya, gap: (d.erp != null && d.mya != null) ? d.erp - d.mya : null,
             back: { plcAc: r25.usable ? r25.plcPerAcre : null, arcAc: arc25,
                     // CY2025's crossover off the SETTLED rate where FSA has paid it — the engine's rule
                     breakeven: breakevenPaymentYield(a25 && a25.payment_rate != null ? +a25.payment_rate : (r25.usable ? r25.arcRate : null), r25.usable ? r25.plcRate : null) } };
  };

  // ⚠ THE SHEET IS BUILT FROM THE SAME RESOLVED ROWS THE SCREEN USES, including the
  // irrigation answer — so a printed sheet can never disagree with the page it was
  // printed from. The settled year is found by looking for a published payment rate
  // rather than assumed to be "last year": FSA settles a crop year about a year after
  // harvest, so which year is settled moves, and a hardcoded one would silently print
  // a blank section the day it rolls.
  //
  // ⚠ AND THE SAME COUNTY YIELD. This passed `yieldFactor: 1` — a normal year — while
  // the screen above priced NY corn at the Yield Model's read, so a sheet printed from
  // that page disagreed with it. arcYieldFactor is the one rule both read.
  const printArcPlcSheet = () => {
    const elYr = arcYear;                                // the ONE ARC/PLC program-year context
    if (elYr == null) return;
    let settledRow = null, settledYear = null;
    for (const y of Object.keys(cty).map(Number).sort((a, b) => b - a)) {
      const r = resolvePracticeRow(((cty[y] || {})[crop] || {})[stName + "|" + county], irrPctIn).row;
      if (r && r.payment_rate != null) { settledRow = r; settledYear = y; break; }
    }
    const model = buildArcPlcSheet({
      crop, state: stName, county, programYear: elYr,
      practiceRows: caCell(elYr), irrigatedPct: irrPctIn,
      ref: (ref[elYr] || {})[crop] || null,
      settledRow, settledYear,
      paymentYield: payYldIn !== "" && +payYldIn > 0 ? +payYldIn : null,
      yieldFactor: arcYieldFactor(elYr),
    });
    const w = window.open("", "_blank");
    if (!w) return;
    writePrintWindow(w, renderArcPlcSheet(model));
  };

  // ── THE FARM ELECTION SUMMARY — every covered commodity the farm grows, on one page ──────
  // Per FARM, not per county: the chosen farm's 2026 insured crops by county, each priced by the
  // SAME engine calls this page makes, with THIS farm's own irrigated-percentage and FSA payment
  // yield answers where the agent has given them (keyed per farm — BUG-092) and nothing where it
  // has not. No payment yield or base acre is defaulted, estimated or borrowed; the page prints
  // the dividing line instead. The window opens first, on the click, so a popup blocker cannot
  // swallow it while the farm's rows load.
  const [farmSumErr, setFarmSumErr] = useState(null);
  // ⚠ THE PROGRAM YEAR IS THE PANEL'S ONE CONTEXT (arcYear, above) — the same year the
  // recommendation and the one-county sheet read. Nothing here picks a year of its own.
  const printFarmElectionSummary = async () => {
    setFarmSumErr(null);
    const Y = arcYear;                                   // the ONE ARC/PLC program-year context
    if (!irrFarmSel || Y == null) return;
    const w = window.open("", "_blank");
    if (!w) return;
    writePrintWindow(w, "<p style=\"font-family:sans-serif;color:#5c6f5c\">Building the farm election summary…</p>");
    try {
      // Every covered commodity the farm grows, for the PICKED program year — one loader
      // (arcPlcFarmSummary.js), shared with tools/arc-plc/farm-summary-pdf.js. This farm's own
      // answers are read through the per-farm key the Advisor writes them under (BUG-092).
      const f = (farmers || []).find(x => x && x.id === irrFarmSel);
      // The farm's own FSA-156EZ for this program year, when one is on file: its tracts'
      // physical counties let the summary mark a county as FSA's rather than a scenario.
      const baseAcreCounties = await fsaBaseAcreCounties(irrFarmSel, Y);
      const model = await loadFarmElectionSummary(sbFetch, {
        farmerId: irrFarmSel, farmerName: (f && (f.name || f.farmer_name)) || null, programYear: Y, baseAcreCounties,
        answersFor: (cr, st, co) => {
          const k = irrigationAnswerKey(irrFarmSel, cr, st, co);
          return { irrigatedPct: irrigationAnswerOf(irrPctBy, k), paymentYield: irrigationAnswerOf(payYldBy, k) };
        },
      });
      writePrintWindow(w, renderFarmElectionSummary(model));
    } catch (e) {
      const msg = String((e && e.message) || e);
      setFarmSumErr(msg);
      try { w.close(); } catch { /* the window may already be gone */ }
    }
  };

  const gridCols = "96px repeat(3, minmax(172px, 1fr))";
  const sect = { background:"#fff", border:"1px solid #d5e2d5", borderRadius:12, padding:isMobile ? "14px 12px" : "16px 18px", marginBottom:18 };

  // ── Per-farm risk: APH CV (own-yield volatility) + basis-to-county (SCO/ECO fit) ──
  //
  // WARNING: THE APH COLUMN USED TO BE THE MEAN OF THE FARM'S OWN YEARLY YIELDS, and
  // the quote next door uses the APPROVED APH, so one farm read two different
  // basis figures on two screens. Kevin, 2026-09-10, on RAUBERDALE STEUBEN: the
  // county book said 1.11x and the quote said 1.18x. Same county figure (76.3 bu);
  // the numerator was 84.5 here against the approved 90 there.
  //
  // The approved APH is the right one, and not merely because the quote uses it:
  // `expected_yield` is RMA's FORWARD expectation for the county, so the matched
  // numerator is RMA's forward expectation for the farm. A backward-looking mean
  // of realised yields against a forward county figure compares two different
  // kinds of number, and the column was labelled APH either way.
  //
  // WARNING: AND THE ROW HAD TO BE SPLIT BY PRACTICE BEFORE THAT WAS EVEN WELL
  // DEFINED. Measured 2026-09-10 over CY2026/27: keyed only by farm+county, 89 of
  // 213 wheat cells carry more than one distinct APH; keyed by farm+county+
  // practice+crop year, 2 of 343 do. Pooling organic with conventional is the
  // blend this repo forbids everywhere else - MONROE wheat reads 84 conventional
  // against 43 organic, and the pooled mean is a yield on neither unit.
  //
  // WARNING: CORN ALSO SPLITS BY TYPE AND THIS DOES NOT FIX THAT. 76 of 384 corn
  // cells still carry two APHs after the practice split - 180 bu grain beside 25.7
  // TON silage, the Lismore unit-of-measure trap. `unitSplit` refuses to average
  // across a >3x spread and takes the larger-acreage side, which is the rule both
  // syncs already use; production_history carries no type code, so the CV beneath
  // it can still pool grain and silage seasons. That is REPORTED on the row rather
  // than silently averaged, and is a separate fix.
  const farmStats = useMemo(() => {
    if (!county) return { rows: [], countyExp: null, countyExpYear: null };
    const nameById = {};
    (farmers || []).forEach(f => { if (f && f.id) nameById[f.id] = f.name || f.farmer_name || null; });
    // The LATEST published county expectation, matching src/wheat/areaFit.js. The
    // current crop year is the one a quote is written against, and RMA restates
    // this figure every year.
    let countyExp = null, countyExpYear = null;
    for (const y of Object.keys(se).map(Number).sort((a, b) => b - a)) {
      const c = ((se[y] || {})[crop] || {})[stName + "|" + county];
      if (c && c.expected_yield != null) { countyExp = +c.expected_yield; countyExpYear = y; break; }
    }
    const isOrg = (p) => /ORGANIC/.test(String(p || "").toUpperCase());
    const key = (fid, org) => fid + "|" + (org ? "ORG" : "CONV");
    const byFarm = {};                                   // farm|practice → year → {weighted yield accum}
    for (const r of ph) {
      if ((r.county || "").toUpperCase() !== county) continue;
      if (r.state && stName && String(r.state).toUpperCase() !== stName) continue;
      if (r.excluded_from_avg === true) continue;
      const y = r.yield_per_acre != null ? +r.yield_per_acre : null;
      if (y == null || y <= 0) continue;
      const fid = r.farmer_id, yr = r.crop_year; if (!fid || yr == null) continue;
      const ac = r.acres != null && +r.acres > 0 ? +r.acres : 0;
      const F = byFarm[key(fid, isOrg(r.practice))] || (byFarm[key(fid, isOrg(r.practice))] = { fid, org: isOrg(r.practice), yrs: {} });
      const Y = F.yrs[yr] || (F.yrs[yr] = { wy: 0, wa: 0, sy: 0, n: 0 });
      if (ac) { Y.wy += y * ac; Y.wa += ac; } else { Y.sy += y; Y.n++; }
    }
    // The approved APH for one farm|county|practice, from the most recent crop year
    // that states one. Several rows in one cell are acre-weighted - EXCEPT where they
    // span more than 3x, which is a unit-of-measure split (bu/ac against ton/ac) and
    // must never be averaged; the larger-acreage side wins and the row says so.
    const approvedAph = (fid, org) => {
      const rows = (covAph || []).filter(c => String(c.farmer_id) === String(fid)
        && c.county === county && (c.practice_code === "713") === org);
      if (!rows.length) return null;
      const yrsSeen = [...new Set(rows.map(c => c.crop_year).filter(v => v != null))].sort((a, b) => b - a);
      const latest = yrsSeen.length ? rows.filter(c => c.crop_year === yrsSeen[0]) : rows;
      const vals = latest.map(c => c.aph).filter(v => v > 0);
      if (!vals.length) return null;
      const lo = Math.min(...vals), hi = Math.max(...vals);
      if (lo > 0 && hi / lo > 3) {
        const best = latest.slice().sort((a, b) => (b.acres || 0) - (a.acres || 0))[0];
        return { aph: best.aph, source: "coverage", year: best.crop_year, unitSplit: true };
      }
      const wa = latest.reduce((t, c) => t + (c.acres > 0 ? c.acres : 0), 0);
      const aph = wa > 0
        ? latest.reduce((t, c) => t + c.aph * (c.acres > 0 ? c.acres : 0), 0) / wa
        : vals.reduce((t, v) => t + v, 0) / vals.length;
      return { aph, source: "coverage", year: yrsSeen[0] != null ? yrsSeen[0] : null, unitSplit: false };
    };
    const out = [];
    for (const k of Object.keys(byFarm)) {
      const F = byFarm[k];
      const yv = Object.values(F.yrs).map(Y => Y.wa > 0 ? Y.wy / Y.wa : (Y.n > 0 ? Y.sy / Y.n : null)).filter(v => v != null);
      if (yv.length < 3) continue;                       // need ≥3 seasons for a meaningful CV
      const mean = yv.reduce((s, v) => s + v, 0) / yv.length;
      const variance = yv.reduce((s, v) => s + (v - mean) * (v - mean), 0) / (yv.length - 1);
      const cv = mean > 0 ? (Math.sqrt(variance) / mean) * 100 : null;
      // The approved APH where the AIP states one; the realised mean only where it
      // does not, and the row names which - so a fallback never passes as an APH.
      const ap = approvedAph(F.fid, F.org);
      const aph = ap ? ap.aph : mean;
      out.push({ fid: F.fid, org: F.org,
                 name: nameById[F.fid] || "Farm " + String(F.fid).slice(0, 6),
                 n: yv.length, aph, aphSource: ap ? "coverage" : "yield_mean",
                 aphYear: ap ? ap.year : null, unitSplit: !!(ap && ap.unitSplit),
                 yieldMean: mean, cv, basis: countyExp ? aph / countyExp : null });
    }
    return { rows: out.sort((a, b) => (b.cv ?? 0) - (a.cv ?? 0)), countyExp, countyExpYear };
  }, [ph, covAph, crop, stName, county, se, farmers]);

  // Farm Programs' ARC-IC tab: the screener alone, on this view's program-year context, with
  // no payment dollars. The Area Coverage Tracker links here instead of drawing it.
  if (programsTab === "arcic") return (
    <ArcIcScreenerPanel
      farmers={farmers}
      fetchRows={sbFetch}
      programYear={arcYear}
      countyReadFn={countyRead}
      resolvePracticeRowFn={resolvePracticeRow}
      showDollars={false}
    />
  );
  const noDollars = !!programsTab;                       // Farm Programs: ARC/PLC with no payment dollars

  return (
    <div style={{ maxWidth:1240, margin:"0 auto" }}>
      {!programsTab && <>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4, flexWrap:"wrap" }}>
        <h1 style={{ margin:0, color:T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>Coverage Advisor</h1>
        {/* The state badge belongs to the county-level Area tracker, which reads ONE
            state at a time. The Renewals list is book-wide across every state, so
            showing "NY" there claimed a scope the page doesn't have.
            THE QUOTER IS THE SAME MISTAKE ONE VIEW OVER, and it was live: its scope is
            whichever FARM you picked, and a farm need not sit in one state — Craig Metzler
            holds Maryland and Pennsylvania policies and this printed "NY" over both. There
            is no single correct state to show, so it shows none; the farm header beneath
            names every state the farm is written in, and each county row names its own. */}
        {view === "county" && (
          <span style={{ fontSize:11, fontWeight:700, color:"#fff", background:"#2d4a2d", borderRadius:5, padding:"2px 8px" }}>{stName}</span>
        )}
      </div>

      {/* view switcher: county market view ↔ per-policy farm advisor */}
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
        {/* ⚠ PRF IS A PRODUCT, NOT A PEER OF "QUOTER". It sat here beside
            Renewals and Book Scan, which put a crop one level above the
            quoter's own Corn / Soybeans / Wheat row and made it read like a
            different tool. It now lives in that crop row, where an agent
            already goes to pick what they are quoting. `view === "prf"` is
            unchanged, so the portal route and every deep link still work. */}
        {[["farm", "Quoter"], ["renewals", "Renewals"], ["book", "Book Scan"], ["county", "Area Coverage Tracker"]].map(([k, lbl]) => (
          // "Quoter" stays lit while PRF is open, because PRF is now one of the
          // things the quoter quotes. Without this the whole tab row goes dark
          // on the PRF workspace and the page reads as if nothing is selected.
          <button key={k} onClick={() => setView(k)} style={{ fontSize:12.5, fontWeight:700, padding:"7px 14px", borderRadius:8, cursor:"pointer", border:"1px solid " + ((view === k || (k === "farm" && view === "prf")) ? "#2d4a2d" : "#d5e2d5"), background:(view === k || (k === "farm" && view === "prf")) ? "#2d4a2d" : "#fff", color:(view === k || (k === "farm" && view === "prf")) ? "#fff" : "#5c6f5c" }}>{lbl}</button>
        ))}
        {/* Base acres (FSA-156EZ) and CCC-866 live on Farm Programs now; this stays so the
            place an agent already reaches for still gets them there. */}
        {onOpenFarmPrograms && (
          <button onClick={() => onOpenFarmPrograms("base", null)} title="Opens Farm Programs: base acres, CCC-866 and ARC/PLC elections"
            style={{ fontSize:12.5, fontWeight:700, padding:"7px 14px", borderRadius:8, cursor:"pointer", border:"1px solid #d5e2d5", background:"#fff", color:"#5c6f5c" }}>Base Acres ›</button>
        )}
      </div>
      </>}

      {view === "farm" ? <FarmPolicyPanel farmers={farmers} se={se} isMobile={isMobile}
                           batchOpen={batchOpen} setBatchOpen={setBatchOpen} batchSel={batchSel} setBatchSel={setBatchSel}
                           onPrf={() => setView("prf")} />
       : view === "renewals" ? <RenewalRoster crop={crop} onCrop={(k) => { setCrop(k); setCountyName(null); }} isMobile={isMobile}
                                 onQuote={(picks) => { setBatchSel(picks); setBatchOpen(true); setView("farm"); }} />
       // The PRF tab is a workspace now, not a screen. The portal still routes
       // straight to PRFReviewView — see the note on PRFAdvisorView.
       : view === "prf" ? <PRFAdvisorView farmers={farmers} isMobile={isMobile} />
       : view === "book" ? <CoverageBookScan isMobile={isMobile} />
       // FSA-156EZ base acres moved to Farm Programs; a stale "base" tab is sent there (above).
       : view === "base" ? null
       : (<>
      {!programsTab && <div style={{ fontSize:12.5, color:"#5c6f5c", marginBottom:16 }}>ARC/PLC + SCO/ECO trigger tracking, side by side, per county — internal advising tool.{futAsOf ? <span style={{ marginLeft:8, color:"#677167" }}>· grain futures as of {futAsOf}</span> : null}{wxR26 != null ? <span style={{ marginLeft:8, color:"#2d6a2d", fontWeight:700 }}>· Yield Prediction Model: {(miPct(wxR26) >= 0 ? "+" : "") + miPct(wxR26)}% vs normal → {_f1(estY26)} est. actual</span> : null}</div>}

      {/* controls */}
      <div style={{ display:"flex", gap:14, flexWrap:"wrap", alignItems:"flex-end", marginBottom:16 }}>
        <div>
          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:4 }}>Crop</div>
          <select value={crop} onChange={e => { setCrop(e.target.value); setCountyName(null); }} style={{ fontSize:14, padding:"7px 10px", borderRadius:7, border:"1px solid #d5e2d5", background:"#fff", minWidth:150 }}>
            {CA_ORDER.map(k => <option key={k} value={k}>{CA_CROPS[k].label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:4 }}>State</div>
          <select value={stName} onChange={e => { setStateName(e.target.value); setCountyName(null); }} style={{ fontSize:14, padding:"7px 10px", borderRadius:7, border:"1px solid #d5e2d5", background:"#fff", minWidth:130 }}>
            {states.map(s => <option key={s} value={s}>{({ NY:"New York", PA:"Pennsylvania", OH:"Ohio", MD:"Maryland" })[s] || s}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".06em", color:"#5c6f5c", fontWeight:700, marginBottom:4 }}>County</div>
          <select value={county || ""} onChange={e => setCountyName(e.target.value)} style={{ fontSize:14, padding:"7px 10px", borderRadius:7, border:"1px solid #d5e2d5", background:"#fff", minWidth:180 }}>
            {counties.map(c => <option key={c} value={c}>{titleCaseName(c)}</option>)}
          </select>
        </div>
      </div>

      {loading && <div style={{ padding:40, color:"#677167" }}>Loading FSA + RMA data…</div>}
      {err && <div style={{ padding:16, color:"#a5461b", background:"#fbeee6", border:"1px solid #eccdba", borderRadius:10, marginBottom:16 }}>Couldn’t load: {err}</div>}

      {!loading && !err && county && (<>
        {/* ARC / PLC */}
        <div style={sect}>
          <h2 style={{ fontSize:15, margin:"0 0 2px" }}>{programsTab ? "ARC / PLC by county" : "🟩 ARC / PLC"}</h2>
          {/* ⚠ THE STATUTORY CHOICE HAS THREE OPTIONS. FSA offers ARC-CO, PLC and
              ARC-IC (Agriculture Risk Coverage - Individual, whole-farm revenue on the
              producer's OWN yields). Silas models the first two; showing only those two
              reads as though the third does not exist. Kevin, 2026-09-17: "Do not silently
              reduce the statutory/program choice to ARC-CO versus PLC." The line used to call
              ARC-IC unmodelled; the per-farm ARC-IC screener has shipped since, on Farm
              Programs' ARC-IC tab, so the line names it and points there. */}
          <div style={{ fontSize:11, color:"#60735f", marginBottom:6 }}>
            ARC-IC <span style={{ color:"#677167" }}>· whole-farm option, screened per farm on Farm Programs</span>
            {onOpenFarmPrograms && (
              <button onClick={() => onOpenFarmPrograms("arcic", null)}
                style={{ marginLeft:6, fontSize:11, fontWeight:700, color:"#2d4a2d", background:"none", border:"none", cursor:"pointer", textDecoration:"underline", padding:0 }}>Open ARC-IC</button>
            )}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:10 }}>
            <div style={{ fontSize:12, color:"#5c6f5c" }}>FSA farm program · <b>MYA</b> price + <b>RMA-sourced</b> county yield · all from FSA files · settles <b>~October</b></div>
            {/* The election sheet. It prints a THRESHOLD, not an election — the payment
                yield is the grower's to supply from their own FSA record, so the sheet is
                useful with nothing of theirs on file. */}
            {/* ONE program year for every ARC/PLC surface on this panel — and the Full Strategy
                Report, which resolves the same rule from the same session pick. */}
            <select value={arcCtx.year || ""} disabled={!arcCtx.offered.length} aria-label="ARC/PLC program year"
              title={arcCtx.why || ""} onChange={e => pickArcYear(e.target.value)}
              style={{ marginLeft:"auto", fontSize:12, padding:"5px 6px", borderRadius:5, border:"1px solid #cdd9cd", background:"#fff" }}>
              {!arcCtx.offered.length && <option value="">{arcPublished ? "no program year" : "…"}</option>}
              {arcCtx.offered.map(o => <option key={o.year} value={o.year}>{arcPlcYearOptionLabel(o)}</option>)}
            </select>
            <button onClick={() => printArcPlcSheet()} disabled={arcYear == null}
              title={arcYear == null ? (arcCtx.pending ? arcPlcPendingMessage(arcCtx.year) : "No program year is available") : "One page: the ARC-CO scenario payment, the PLC payment-yield breakeven, price sensitivity, what ARC-CO actually paid, and the irrigation assumption where the county is split."}
              style={{ fontSize:12, fontWeight:800, padding:"6px 13px", borderRadius:7, opacity: arcYear == null ? 0.45 : 1,
                       border:"1px solid #1a2e1a", background:"#1a2e1a", color:"#fff", cursor: arcYear == null ? "not-allowed" : "pointer" }}>
              🖨 Election sheet
            </button>
          </div>
          {/* ⚠ WHOSE FARM? TWO FIGURES IN THIS PANEL BELONG TO ONE FARM, NOT TO THE COUNTY:
              the irrigated share FSA blends the benchmark by (7 CFR 1412.53(c)) and the FSA
              payment yield PLC pays on. Both were held page-wide, so answering for one
              grower rated the next grower's figures — in Van Buren MI corn that is a
              $1,068.62 irrigated benchmark against a $806.71 non-irrigated one, $261.91/ac
              decided by whoever was looked at first (BUG-092).
              The selector sits HERE rather than inside the split-county box because the
              payment yield is per farm in every county, split or not — a picker that
              appeared only in split counties would leave that input keyless and dead.
              Nothing is guessed: with no farm chosen both inputs are empty, the county
              figures are withheld where FSA splits, and the payment yield falls back to
              the stated default rather than to the last farm's answer. */}
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10, padding:"7px 10px", background:"#f7faf7", border:"1px solid #e0ece0", borderRadius:7 }}>
            <span style={{ fontSize:12, fontWeight:800, color:"#2d4a2d" }}>Whose farm?</span>
            <select value={irrFarmSel || ""} onChange={e => setIrrFarmId(e.target.value || null)}
              style={{ fontSize:12, padding:"5px 8px", borderRadius:5, border:"1px solid #cdd9cd", background:"#fff", minWidth:220 }}>
              <option value="">— select a farm —</option>
              {irrFarms.map(f => <option key={f.fid} value={f.fid}>{f.name}</option>)}
            </select>
            <span style={{ fontSize:11, color:"#60735f" }}>
              {irrFarms.length
                ? "the irrigated share and the FSA payment yield are per farm, not per county"
                : "no farm with reported history in this county"}
            </span>
            {/* One page for the whole farm: every covered commodity it insures, every county, on
                the same engine as this panel. Needs a farm, so it lives where the farm is chosen. */}
            <button onClick={() => printFarmElectionSummary()} disabled={!irrFarmSel || arcYear == null}
              title={!irrFarmSel ? "Choose a farm first" : arcYear == null ? (arcCtx.pending ? arcPlcPendingMessage(arcCtx.year) : "No program year is available") : "One page for this farm: every covered commodity it insures in the " + arcYear + " program year, every county — the modeled ARC-CO / PLC comparison, key FSA numbers, and the payment-yield and county-yield sensitivity. Nothing about the farm is assumed."}
              style={{ marginLeft:"auto", fontSize:12, fontWeight:800, padding:"6px 13px", borderRadius:7,
                       border:"1px solid #1a2e1a", background: irrFarmSel ? "#fff" : "#f0f3f0", color: irrFarmSel ? "#1a2e1a" : "#677167", cursor: irrFarmSel ? "pointer" : "not-allowed" }}>
              🖨 Farm election summary
            </button>
            <button onClick={() => { if (onOpenFarmPrograms) onOpenFarmPrograms("base", irrFarmSel); }} disabled={!irrFarmSel || !onOpenFarmPrograms}
              title={!irrFarmSel ? "Choose a farm first" : "This farm's FSA-156EZ: base acres, PLC yields and ARC/PLC elections, per FSA farm"}
              style={{ fontSize:12, fontWeight:800, padding:"6px 13px", borderRadius:7, border:"1px solid #1a2e1a", background: irrFarmSel ? "#fff" : "#f0f3f0", color: irrFarmSel ? "#1a2e1a" : "#677167", cursor: irrFarmSel ? "pointer" : "not-allowed" }}>
              Base acres (156EZ / CCC-866)
            </button>
            {farmSumErr && <span style={{ fontSize:11, color:"#a5461b" }}>Couldn’t build the summary: {farmSumErr}</span>}
          </div>
          {/* ⚠ THE IRRIGATION QUESTION, ASKED ONLY WHERE IT CHANGES THE ANSWER.
              FSA publishes separate irrigated and non-irrigated benchmarks in some
              counties and NO "ALL" row for them, and 7 CFR 1412.53(c) says it BLENDS
              the two by the farm's own irrigated share of reported acres rather than
              choosing one. Until this shipped the page kept whichever row arrived
              first — Van Buren MI corn reads $1,068.62 irrigated against $806.71
              non-irrigated, decided by nothing.
              Almost every farm is one or the other, so those are one click; the box is
              for the minority that are genuinely mixed, because rounding a mixed farm
              onto a side is the same invention in a smaller coat. Unanswered shows the
              two figures and no single one. */}
          {/* PENDING: the active election is a program year FSA has not published figures for.
              Say so, and compute nothing — a previous year is never substituted. */}
          {arcCtx.pending ? (
            <div style={{ border:"1px solid #e6c98a", background:"#fdf8ec", borderRadius:8, padding:"10px 13px", marginBottom:10, fontSize:12.5, color:"#6b4f12" }}>
              <b>{arcPlcPendingMessage(arcCtx.year)}</b>
              {(() => { const prev = arcCtx.offered.find(o => o.status === "previous" && o.published);
                return prev ? <div style={{ fontSize:11.5, marginTop:4, color:"#7a6a3a" }}>The {prev.year} program year remains available in the picker above, labelled as the previous election.</div> : null; })()}
            </div>
          ) : null}
          {(() => {
            const rs = arcYear != null ? caRes(arcYear) : null;
            if (!rs || !rs.split) return null;
            const one = (v, lbl) => (
              <button key={lbl} onClick={() => setIrrPctIn(v)}
                style={{ fontSize:12, fontWeight:700, padding:"5px 11px", borderRadius:5, cursor:"pointer",
                         border:"1px solid " + (irrPctIn === v ? "#1f4e6b" : "#cdd9cd"),
                         background: irrPctIn === v ? "#1f4e6b" : "#fff",
                         color: irrPctIn === v ? "#fff" : "#5c6f5c" }}>{lbl}</button>
            );
            const dollars = n => n == null ? "—" : "$" + Math.round(n);
            return (
              <div style={{ border:"1px dashed #1f4e6b", background:"#f2f7fa", borderRadius:7, padding:"10px 12px", marginBottom:10 }}>
                <div style={{ fontSize:11, fontWeight:800, letterSpacing:".05em", textTransform:"uppercase", color:"#1f4e6b", marginBottom:5 }}>
                  FSA splits this county by irrigation practice
                </div>
                <div style={{ fontSize:12, color:"#40525c", lineHeight:1.45, marginBottom:8 }}>
                  There is no single county figure here — FSA blends the two by this farm's own
                  irrigated share of reported acres (7 CFR 1412.53(c)).{noDollars ? null : <>{" "}Benchmark revenue is{" "}
                  <b>{dollars(rs.irrigated && rs.irrigated.benchmark_revenue)}</b> irrigated against{" "}
                  <b>{dollars(rs.nonIrrigated && rs.nonIrrigated.benchmark_revenue)}</b> non-irrigated.</>}
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", opacity: irrFarmSel ? 1 : 0.45, pointerEvents: irrFarmSel ? "auto" : "none" }}>
                  {one(0, "All non-irrigated")}
                  {one(100, "All irrigated")}
                  <span style={{ fontSize:12, color:"#5c6f5c", marginLeft:4 }}>or mixed</span>
                  <input type="number" min="0" max="100" placeholder="% irr"
                    value={irrPctIn != null && irrPctIn !== 0 && irrPctIn !== 100 ? irrPctIn : ""}
                    onChange={e => { const v = e.target.value; setIrrPctIn(v === "" ? null : Math.max(0, Math.min(100, +v))); }}
                    style={{ width:78, fontSize:12, padding:"5px 7px", border:"1px solid #cdd9cd", borderRadius:5 }} />
                  {irrPctIn != null && (
                    <button onClick={() => setIrrPctIn(null)}
                      style={{ fontSize:11, color:"#60735f", background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>clear</button>
                  )}
                </div>
                {irrPctIn == null && (
                  <div style={{ fontSize:11.5, color:"#8a3324", marginTop:7, fontWeight:600 }}>
                    {irrFarmSel
                      ? "Not answered for this farm — the figures below are withheld rather than taken from one side."
                      : "No farm selected — the figures below are withheld. The irrigated share belongs to a farm, so it is not carried over from the last one."}
                  </div>
                )}
              </div>
            );
          })()}
          {(() => {
            const rec = arcPlcRec(); if (!rec) return null;
            const cty1 = county.charAt(0) + county.slice(1).toLowerCase();
            // THE STRUCTURAL COMPARISON — the farm election summary's own cell for this county, worded by
            // the summary's own sentence, so the two surfaces cannot price or word one county two ways.
            // It does NOT depend on USDA's projected MYA being low enough for either program to pay: the
            // farmer still elects (Kevin, 2026-09-19). The payment yield is OPTIONAL — without it this is
            // the dividing line or the price condition, never a number of ours.
            const arcStructure = (withInput) => {
              const c = rec.cell;
              if (!c || !c.usable || !c.comparison || !c.comparison.usable) return null;
              const k = c.keyNumbers, cmp = c.comparison;
              const $2 = v => v == null ? "—" : "$" + Number(v).toFixed(2);
              const asOf = k.myaAsOf ? new Date(k.myaAsOf + "T12:00:00").toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : null;
              const scen = (x) => x.scenario === "silas" ? "Silas scenario " + Math.round(x.factor * 100) + "%" : x.scenario === "benchmark" ? "100% (benchmark)" : Math.round(x.factor * 100) + "%";
              return (
                <div style={{ border:"1px solid #d8e6d8", background:"#fff", borderRadius:8, padding:"8px 10px", margin:"6px 0 8px" }}>
                  <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:".05em", textTransform:"uppercase", color:"#2d4a2d" }}>Modeled comparison <span style={{ textTransform:"none", letterSpacing:0, fontWeight:500, color:"#60735f" }}>· {rec.year} program year · at benchmark county yield</span></div>
                  <div style={{ fontSize:12.5, fontWeight:600, color:"#1f2a1f", margin:"3px 0 5px" }}>{comparisonSentence(c)}</div>
                  {cmp.kind === "arcFirst" && cmp.lines.length ? (
                    <div style={{ fontSize:11.5, color:"#3a4a3a", marginBottom:4 }}>PLC's modeled payment is larger if the MYA falls below: {cmp.lines.map((l, i) => <span key={i}>{i ? " · " : ""}<b>{$2(l.plcLargerBelow)}</b> at a {l.paymentYield} bu payment yield</span>)} <span style={{ color:"#60735f" }}>(ruler marks at benchmark county yield — not this farm's yield)</span></div>
                  ) : null}
                  <div style={{ fontSize:11.5, color:"#3a4a3a", lineHeight:1.55 }}>
                    ARC benchmark price <b>{$2(k.arcBenchmarkPrice)}</b> · ARC price trigger at benchmark county yield <b>{$2(k.arcPriceTriggerAtBenchmarkYield)}</b> · PLC effective reference price <b>{$2(k.plcEffectiveReferencePrice)}</b> · USDA projected MYA <b>{$2(k.myaProjection)}</b>{asOf ? <span style={{ color:"#60735f" }}> (projection as of {asOf})</span> : null}
                  </div>
                  {/* County-yield sensitivity — the one-pager's strip, same rows, same cells. */}
                  {(() => { const xs = (c.countyYieldSensitivity || []).filter(x => x.usable); if (!xs.length) return null;
                    const td = { padding:"2px 7px", textAlign:"right", borderTop:"1px solid #edf2ed" };
                    const cz = (x) => x.kind === "threshold" ? Math.round(x.switchYield) + " bu" : x.kind === "arcFirst" ? "ARC-CO first" : "—";
                    return (
                      <table style={{ fontSize:11, color:"#3a4a3a", marginTop:6, borderCollapse:"collapse" }}><tbody>
                        <tr><td style={{ ...td, textAlign:"left", color:"#60735f" }}>County yield</td>{xs.map((x, i) => <td key={i} style={{ ...td, fontWeight:700, color: x.scenario === "silas" ? "#2d5a8a" : "#3a4a3a" }}>{scen(x)}<div style={{ fontWeight:500 }}>≈{Math.round(x.countyYield)} bu</div></td>)}</tr>
                        <tr><td style={{ ...td, textAlign:"left" }}>ARC-CO pays if MYA below</td>{xs.map((x, i) => <td key={i} style={td}>{$2(x.arcStartsBelow)}</td>)}</tr>
                        {!noDollars && <tr><td style={{ ...td, textAlign:"left" }}>ARC-CO $/base ac at {$2(k.myaProjection)}</td>{xs.map((x, i) => <td key={i} style={td}>{x.arcPerBaseAcreAtProjection == null ? "—" : $2(x.arcPerBaseAcreAtProjection) + (x.arcCappedAtProjection ? " cap" : "")}</td>)}</tr>}
                        <tr><td style={{ ...td, textAlign:"left" }}>PLC larger if payment yield above</td>{xs.map((x, i) => <td key={i} style={td}>{cz(x)}</td>)}</tr>
                      </tbody></table>
                    ); })()}
                  {withInput ? (
                    <label style={{ display:"inline-block", marginTop:6, fontSize:11.5, fontWeight:700, color:"#5c6f5c" }}>Farm's FSA PLC payment yield <span style={{ fontWeight:500 }}>(optional — sharpens the comparison, never required)</span>{" "}
                      <input type="number" value={payYldIn} onChange={e => setPayYldIn(e.target.value)} placeholder="FSA"
                        style={{ width:60, fontSize:12, padding:"3px 6px", borderRadius:6, border:"1px solid #cfe0cf" }} /> bu
                      {payYldIn !== "" ? <button onClick={() => setPayYldIn("")} style={{ marginLeft:6, fontSize:10, color:"#60735f", background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>reset</button> : null}
                    </label>
                  ) : null}
                </div>
              );
            };
            // Farm Programs: the program call and the modeled comparison (prices, payment-yield
            // lines, county-yield sensitivity), and none of the per-acre payment figures below.
            if (noDollars) {
              const yl = cropYearLabel(crop, rec.year) || {}, comparison = arcStructure(true);
              return (
                <div style={{ border:"1px solid #cfe3cf", borderRadius:10, background:"#f4faf4", padding:"11px 13px", marginBottom:12 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#1f5c2e" }}>
                    Title program → <span style={{ color:"#33402f" }}>{rec.enrolText || rec.label}</span>
                    <span style={{ fontWeight:600, color:"#3a6a3a", fontSize:11.5 }}> · {cty1} {crop.toLowerCase()} · {yl.crop || rec.year + " crop"} · pays {yl.pays || "—"}</span>
                  </div>
                  {comparison}
                </div>
              );
            }
            const dv = n => n == null ? "—" : "$" + Math.round(n);
            const be = rec.breakeven, eff = rec.effYld;
            // The engine's pick, or none: without the farm's payment yield a live county has a
            // CONDITION, not a winner, and the header prints the condition (rec.label).
            const isPLC = rec.pick === "PLC";
            const margin = (rec.plcAc != null && rec.arcAc != null) ? Math.abs(rec.plcAc - rec.arcAc) : null;
            const bes = (rec.beCurve || []).map(p => p.be);
            const bandLo = bes.length ? Math.min(...bes) : be, bandHi = bes.length ? Math.max(...bes) : be;
            // ⚠ NO BAR IS AN ANSWER, AND IT HAS TO SAY SO. The bar asks one question —
            // at what payment yield does PLC overtake ARC-CO — and when MYA sits above
            // the reference price PLC pays $0 at EVERY yield, so no crossover exists and
            // `breakeven` is null. Drawing a bar there would invent a decision.
            //
            // Kevin, on Livingston soybeans: "Why does coverage advisor soybeans have no
            // color bar?" The numbers were right and the ABSENCE was unexplained, so a
            // correct answer read as a broken widget beside corn, which does draw one.
            if (be == null || rec.arcAc == null) {   // no crossover exists → say why, don't draw one
              const why = rec.plcRate > 0
                ? "ARC-CO has no county revenue shortfall to pay on, so there is no yield at which the two cross."
                : "PLC pays nothing at ANY payment yield while MYA is above the reference price, so there is no crossover to draw.";
              return <div style={{ border:"1px solid #cfe3cf", borderRadius:10, background:"#f4faf4", padding:"11px 13px", marginBottom:12, fontSize:12, color:"#3a4a3a" }}>
                {rec.enrolText ? <div style={{ fontSize:13, fontWeight:800, color:"#1f5c2e", marginBottom:5 }}>Title program → <span style={{ color:"#33402f" }}>{rec.enrolText}</span><span style={{ fontWeight:600, color:"#3a6a3a", fontSize:11.5 }}> · {(cropYearLabel(crop, rec.year) || {}).crop || rec.year + " crop"} · pays {(cropYearLabel(crop, rec.year) || {}).pays || "—"}</span></div> : null}
                {arcStructure(true)}
                At USDA's forecast: MYA {_f(rec.mya)} vs {_f(rec.erp)} reference — PLC {rec.plcRate > 0 ? "pays ~" + _f(rec.plcRate) + "/bu" : "doesn't trigger"}; ARC-CO {dv(rec.arcAc)}/ac. SCO + ECO on top either way.
                <div style={{ marginTop:6, fontSize:11, color:"#60735f", lineHeight:1.5 }}>
                  <b>No crossover at USDA's projected MYA</b> — {why} The election still has to be made: the modeled comparison above describes it across prices, not only at this year's projection. The price scale appears whenever one program overtakes the other at the projected MYA.
                </div></div>;
            }
            // Axis is anchored on the county BENCHMARK yield (a realistic payment-yield scale), NOT the
            // breakeven — which can be absurd when PLC barely triggers (e.g. corn). Expand only to keep the
            // handle in view. An off-scale breakeven then just means one program wins across all real yields.
            const benY = rec.proxyYld != null ? rec.proxyYld : (eff != null ? eff : (be != null ? be : 100));
            let axLo = Math.max(0, Math.floor(benY * 0.5 / 10) * 10);
            let axHi = Math.ceil(benY * 1.15 / 10) * 10;
            if (eff != null && eff < axLo + 5) axLo = Math.max(0, Math.floor((eff - 8) / 10) * 10);
            if (eff != null && eff > axHi - 5) axHi = Math.ceil((eff + 8) / 10) * 10;
            const P = y => Math.max(0, Math.min(100, (y - axLo) / (axHi - axLo) * 100));
            const beOn = be != null && be >= axLo && be <= axHi;   // breakeven on a realistic scale?
            const gold = "#c98a1a", green = "#2f8a48";
            return (
              <div style={{ border:"1px solid #cfe3cf", borderRadius:10, background:"#f4faf4", padding:"12px 14px", marginBottom:12 }}>
                {/* header: the call + live price */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:8, marginBottom:2 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#1f5c2e" }}>
                    Title program → {rec.enrolText ? <span style={{ color:"#33402f" }}>{rec.enrolText}</span> : rec.pick ? <span style={{ color:isPLC ? green : gold }}>{rec.pick}</span> : <span style={{ color:"#33402f" }}>{rec.label}</span>}
                    <span style={{ fontWeight:600, color:"#3a6a3a", fontSize:11.5 }}> · {cty1} {crop.toLowerCase()} · {(cropYearLabel(crop, rec.year) || {}).crop || rec.year + " crop"} · pays {(cropYearLabel(crop, rec.year) || {}).pays || "—"} · <b>+ SCO + ECO</b> either way</span>
                  </div>
                  <div style={{ fontSize:10.5, color:"#60735f" }}>MYA {_f(rec.mya)}{yr(rec.year).final ? "" : " est"}{rec.hv.px != null ? <> · {meta.label} {_f(rec.hv.px)} <span style={{ color:"#677167" }}>({rec.hv.tag}{!rec.hv.final && futAsOf ? " " + futAsOf : ""})</span></> : null}</div>
                </div>

                {arcStructure(false)}
                {/* the decision scale: payment yield → ARC-CO (low) ▸ PLC (high) */}
                <div style={{ position:"relative", height:60, margin:"14px 4px 4px" }}>
                  {/* track with gold→(price-band)→green zones */}
                  {/* ⚠ THE SPLIT IS THE BREAKEVEN — the same number the marker sits against,
                      the pick is made on and the sentence below prints. It used to be painted
                      from the price-sensitivity band, which is computed at a different county
                      yield, so the colours contradicted the headline. When the breakeven is
                      off the realistic scale the bar is ONE colour, because that is the answer:
                      one program wins across every yield a farm could have. */}
                  <div style={{ position:"absolute", left:0, right:0, top:22, height:16, borderRadius:8,
                    background: beOn
                      ? `linear-gradient(90deg, ${gold} 0%, ${gold} ${P(be)}%, ${green} ${P(be)}%, ${green} 100%)`
                      : (be != null && be > axHi ? gold : green) }} />
                  {/* Price sensitivity, as an OVERLAY and never as the split: how far the
                      breakeven slides across plausible MYA, holding the county yield where
                      the estimate above put it. Drawn only when it is on scale and wide
                      enough to mean something. */}
                  {beOn && bandHi - bandLo > 2 && bandLo < axHi && bandHi > axLo ? (
                    <div title={`The crossover moves between ${bandLo.toFixed(0)} and ${bandHi.toFixed(0)} bu across plausible MYA prices`}
                      style={{ position:"absolute", top:22, height:16, left:`${P(bandLo)}%`, width:`${Math.max(0, P(bandHi) - P(bandLo))}%`,
                               background:"repeating-linear-gradient(45deg, rgba(255,255,255,.55) 0 3px, rgba(255,255,255,0) 3px 6px)",
                               borderLeft:"1px solid rgba(51,64,47,.35)", borderRight:"1px solid rgba(51,64,47,.35)" }} />
                  ) : null}
                  <div style={{ position:"absolute", left:2, top:4, fontSize:10.5, fontWeight:800, color:gold }}>◀ ARC-CO</div>
                  <div style={{ position:"absolute", right:2, top:4, fontSize:10.5, fontWeight:800, color:green }}>PLC ▶</div>
                  {/* price-sensitivity band label (only when the band is on-scale) */}
                  {beOn && bandHi - bandLo > 2 && bandLo < axHi && bandHi > axLo ? <div style={{ position:"absolute", left:`${(P(bandLo) + P(bandHi)) / 2}%`, top:4, transform:"translateX(-50%)", fontSize:9.5, color:"#8a7a5a", whiteSpace:"nowrap" }}>moves with price</div> : null}
                  {/* breakeven line (current MYA) — or an off-scale note */}
                  {beOn ? <>
                    <div style={{ position:"absolute", left:`${P(be)}%`, top:18, height:24, width:2, background:"#33402f", transform:"translateX(-1px)" }} />
                    <div style={{ position:"absolute", left:`${P(be)}%`, top:44, transform:"translateX(-50%)", fontSize:10, color:"#5c6f5c", whiteSpace:"nowrap" }}>{be.toFixed(0)} at forecast</div>
                  </> : (be != null && be > axHi
                    ? <div style={{ position:"absolute", right:2, top:44, fontSize:10, fontWeight:700, color:gold, whiteSpace:"nowrap" }}>ARC-CO across realistic yields</div>
                    : <div style={{ position:"absolute", left:2, top:44, fontSize:10, fontWeight:700, color:green, whiteSpace:"nowrap" }}>PLC across realistic yields</div>)}
                  {/* draggable handle at effective yield */}
                  {eff != null ? <div style={{ position:"absolute", left:`${P(eff)}%`, top:14, transform:"translateX(-50%)", textAlign:"center", pointerEvents:"none" }}>
                    <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", border:`3px solid ${isPLC ? green : gold}`, boxShadow:"0 1px 3px rgba(0,0,0,.25)", margin:"0 auto" }} />
                    <div style={{ fontSize:11, fontWeight:800, color:"#1a2e1a", marginTop:1, whiteSpace:"nowrap" }}>{eff.toFixed(0)} bu</div>
                  </div> : null}
                  {/* invisible range input drives the handle */}
                  <input type="range" min={axLo} max={axHi} step={1} value={Math.round(eff != null ? eff : Math.max(axLo, Math.min(axHi, be)))} onChange={e => setPayYldIn(e.target.value)}
                    style={{ position:"absolute", left:0, right:0, top:18, width:"100%", height:24, margin:0, opacity:0, cursor:"pointer" }} />
                  <div style={{ position:"absolute", left:0, top:44, fontSize:9.5, color:"#677167" }}>{axLo}</div>
                  <div style={{ position:"absolute", right:0, top:44, fontSize:9.5, color:"#677167" }}>{axHi} bu</div>
                </div>

                {/* readout + payment-yield input */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, marginTop:8 }}>
                  <div style={{ fontSize:12, color:"#3a4a3a" }}>
                    {eff != null ? <>At <b>{eff.toFixed(0)} bu</b>: PLC <b style={{ color:green }}>{dv(rec.plcAc)}</b> vs ARC-CO <b style={{ color:gold }}>{dv(rec.arcAc)}</b>/ac →{" "}
                    <b style={{ color:isPLC ? green : gold }}>{rec.pick || "—"}{margin != null ? ` by ${dv(margin)}/ac` : ""}</b></>
                    : <>At USDA's forecast: PLC pays more above <b>{Math.round(be)} bu</b> of payment yield · ARC-CO <b style={{ color:gold }}>{dv(rec.arcAc)}</b>/ac</>}
                  </div>
                  <label style={{ fontSize:11.5, fontWeight:700, color:"#5c6f5c" }}>Farm payment yield{" "}
                    <input type="number" value={payYldIn} onChange={e => setPayYldIn(e.target.value)} placeholder="FSA"
                      style={{ width:60, fontSize:12, padding:"3px 6px", borderRadius:6, border:"1px solid #cfe0cf" }} /> bu
                    {payYldIn !== "" ? <button onClick={() => setPayYldIn("")} style={{ marginLeft:6, fontSize:10, color:"#60735f", background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>reset</button> : null}
                  </label>
                </div>
                {rec.noYield ? <div style={{ fontSize:10, color:"#6f7f6f", marginTop:4 }}>No payment yield is assumed. Enter the farm's own — it is on its FSA farm record, and it is not the APH — or drag the handle, to see which program pays more at it.</div> : null}
                {rec.yieldBasis === "scenario" ? <div style={{ fontSize:10, color:"#6f7f6f", marginTop:2 }}>ARC-CO here is priced at the Yield Model's read of this county's yield against its FSA benchmark — a scenario, not an FSA figure. The printed election sheet uses the same.</div> : null}

                {/* the two things that don't change with the slider */}
                <ul style={{ margin:"8px 0 0", paddingLeft:16, fontSize:11, color:"#5c6f5c", lineHeight:1.5, borderTop:"1px solid #e0ece0", paddingTop:7 }}>
                  <li><b>SCO + ECO ride on either title</b> — complements, not overlap: SCO uses futures/harvest prices on planted acres, ARC-CO the MYA cash price on base acres (2023: SCO paid, ARC-CO didn't). Widest gap for wheat.</li>
                  {/* Both halves of this line used to be wheat's numbers hard-typed into prose
                      that rendered on every crop tab: "MYA final" is only true where mya_status
                      says so (2025 corn and soybeans are still FORECAST until their Sep-Aug
                      marketing year closes), and the "~52 bu / $1.29" breakeven was wheat's
                      2025 PLC rate shown against corn's $0.27. Both now come from the data. */}
                  <li><b>Reference — CY{rec.backYear} (already elected, pays {(cropYearLabel(crop, rec.backYear) || {}).pays || "—"}, MYA {yr(rec.backYear).final ? "final" : "still forecast"}):</b> PLC {_f(yr(rec.backYear).plc)}/bu{eff != null ? <> (≈{dv(rec.back.plcAc)}/ac at {eff.toFixed(0)} bu)</> : null} vs ARC-CO {dv(rec.back.arcAc)}/ac in {cty1}.{(() => {
                    const p25 = yr(rec.backYear).plc, be25 = rec.back.breakeven;
                    if (be25 == null || !isFinite(be25)) return null;
                    return <> Breakeven then was ~{Math.round(be25)} bu (PLC was {_f(p25)}) — {rec.plcRate != null && p25 != null ? (rec.plcRate < p25 ? "this year's is higher because " + rec.year + " PLC is smaller" : "this year's is lower because " + rec.year + " PLC is larger") : "compare against this year's"}.</>;
                  })()}</li>
                </ul>
              </div>
            );
          })()}
          {!noDollars && <div style={{ overflowX:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:gridCols, gap:9, minWidth:700, alignItems:"stretch" }}>
              {/* ⚠ ALL THREE ARE CROP YEARS, and they used to carry three different KINDS
                  of sub-label — "pays Oct 26", "election open", "recommend" — so a reader
                  had to know which of crop year / election year / payment year each bare
                  number meant. An ARC/PLC year pays the October AFTER its harvest, so each
                  column now names that outright. */}
              <div />{YrHead(2025, "crop year · pays Oct 2026", "my")}{YrHead(2026, "crop year" + arcElecTag(2026) + " · pays Oct 2027", "my")}{hasAP(2027) ? YrHead(2027, "crop year · established · pays Oct 2028", "my") : YrHead(2027, "crop year · recommend · pays Oct 2028")}
              {RLabel("PLC", "price only")}{plcCell(2025)}{plcCell(2026)}
              {hasAP(2027) ? plcCell(2027) : <Cell edge chip="Rec" chipBg="#eae7fb" chipColor="#5b47b0" rows={[["Reference Price", <>{_f(yr(2026).stat)}{badge("Set")}</>], ["Basis", "statutory floor"], ["PLC Rate", pend("needs 27/28 MYA")]]} verdict="reference set · MYA pending" vBg="#eae7fb" vColor="#5b47b0" />}
              {RLabel("ARC-CO", "county revenue")}{arcCell(2025)}{arcCell(2026)}
              {hasAP(2027) ? arcCell(2027) : <Cell edge chip="Rec" chipBg="#eae7fb" chipColor="#5b47b0" rows={[["Benchmark Price", "Olympic avg, 5-yr"], ["Yield source", "RMA (not NASS)"], ["Guarantee", "90% of benchmark"]]} verdict="compare when FSA publishes" vBg="#eae7fb" vColor="#5b47b0" />}
            </div>
          </div>}
        </div>

        {/* ⚠ ARC-IC IS SCOPED DIFFERENTLY ON PURPOSE: the whole farm in one state, across every
            covered commodity, with its own farm picker over the whole book. It is drawn on Farm
            Programs' ARC-IC tab (the early return above, same program-year context); this row
            is the way there from where agents used to find it. */}
        {!programsTab && (
          <div style={{ ...sect, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:800, color:"#1a2e1a" }}>ARC-IC farm screener</span>
            <span style={{ fontSize:12, color:"#5c6f5c" }}>whole farm, per state · on Farm Programs</span>
            {onOpenFarmPrograms && (
              <button onClick={() => onOpenFarmPrograms("arcic", null)}
                style={{ marginLeft:"auto", fontSize:12, fontWeight:800, padding:"6px 13px", borderRadius:7, border:"1px solid #1a2e1a", background:"#fff", color:"#1a2e1a", cursor:"pointer" }}>Open ARC-IC</button>
            )}
          </div>
        )}

        {!programsTab && <>
        {/* SCO / ECO */}
        <div style={sect}>
          <h2 style={{ fontSize:15, margin:"0 0 2px" }}>🟦 SCO / ECO</h2>
          <div style={{ fontSize:12, color:"#5c6f5c", marginBottom:10 }}>RMA area endorsement · <b>projected + harvest</b> price + <b>RMA area</b> yield · settles <b>~June</b></div>
          <div style={{ overflowX:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:gridCols, gap:9, minWidth:700, alignItems:"stretch" }}>
              {/* THE BANDS MOVED FOR 2027 and the recommendation column is the one being sold
                  right now (wheat SCD Sep 30 2026). Verified in the ADM's A01130_AreaCoverageLevel,
                  not assumed: 2026 SCO rows carry Area Loss Start 0.86 and ECO-95 runs 0.95→0.86;
                  2027 SCO rows carry Area Loss Start 0.90 and ECO-95 runs 0.95→0.90. So SCO's top
                  rose 86→90 (OBBBA) and ECO narrowed to a 5-point band sitting straight on top of
                  it. The settled columns keep the bands those years actually carried — that is what
                  RMA paid on — so only the 2027 column moves. `backtestScoEco` already runs 90/95→90.
                  Quoting a grower an 86% SCO start for 2027 understates what he is buying. */}
              {/* The 2026 caption said "tracking" even after the harvest price was announced.
                  What is still open then is the county YIELD, which publishes ~Feb. */}
              <div />{YrHead(2025, "paid Jun 26", "px")}{YrHead(2026, harvestFinal ? "yield pending" : "tracking", "px")}{YrHead(2027, "recommend")}
              {RLabel("ECO-95", "95% → 86% · '27: 90%")}{scoEcoCell(2025, sd ? +sd.eco95_payout_pct : 0, 0.95, 0.86, "ECO Trigger (95%)")}{scoEcoCell(2026, 0, 0.95, 0.86, "ECO Trigger (95%)")}
              <Cell edge chip="Rec" chipBg="#eae7fb" chipColor="#5b47b0" rows={[["Band", <b>95% → 90%</b>], ["Change", <span style={{ color:"#a5461b", fontWeight:700 }}>was 95→86</span>], ["SCD", crop === "WHEAT" ? "Sept 30, 2026" : "Mar 15, 2027"]]} verdict="ECO-95 · 5-pt band" vBg="#eae7fb" vColor="#5b47b0" />
              {RLabel("SCO", "86% → MPCI · '27: 90%")}{scoEcoCell(2025, sd ? +sd.sco_payout_pct : 0, 0.86, 0.75, "SCO Trigger (86%)")}{scoEcoCell(2026, 0, 0.86, 0.75, "SCO Trigger (86%)")}
              <Cell edge chip="Rec" chipBg="#eae7fb" chipColor="#5b47b0" rows={[["Band", <b>90% → MPCI</b>], ["Change", <span style={{ color:"#a5461b", fontWeight:700 }}>top 86 → 90</span>], ["Stacks", "on ARC or PLC"]]} verdict="wider · stackable" vBg="#eae7fb" vColor="#5b47b0" />
            </div>
          </div>
          {/* Historical trigger backtest — today's 90/95 product applied to every prior year */}
          <ScoEcoBacktest stateFips={stName} county={county} crop={crop} baseCl={0.75} />
        </div>

        {/* Per-farm risk — the first individual-farm layer (APH volatility + basis to county) */}
        {farmStats.rows.length > 0 && (() => {
          const cvColor = cv => cv == null ? "#8a8f8a" : cv < 15 ? "#1f7a3d" : cv < 25 ? "#b8860b" : "#a5461b";
          const basisColor = b => b == null ? "#8a8f8a" : (b >= 0.9 && b <= 1.1) ? "#1a2e1a" : "#a5461b";
          const med = arr => { const a = arr.filter(v => v != null).sort((x, y) => x - y); const m = a.length >> 1; return a.length ? (a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2) : null; };
          const medCv = med(farmStats.rows.map(r => r.cv)), medB = med(farmStats.rows.map(r => r.basis));
          const th = { padding:"6px 10px", fontSize:10, textTransform:"uppercase", letterSpacing:".05em", color:"#677167", fontWeight:700 };
          const td = { padding:"6px 10px", fontSize:13, borderTop:"1px solid #eef3ee" };
          return (
            <div style={sect}>
              <h2 style={{ fontSize:15, margin:"0 0 2px" }}>👨‍🌾 Per-farm risk — {titleCaseName(county)} {crop.toLowerCase()} book <span style={{ fontSize:12, fontWeight:500, color:"#677167" }}>({farmStats.rows.length} {farmStats.rows.length === 1 ? "farm" : "farms"})</span></h2>
              <div style={{ fontSize:12, color:"#5c6f5c", marginBottom:10 }}>
                <b>APH CV</b> = the farm's own yield volatility — higher → more own-farm coverage (MPCI level) earns its keep. <b>Basis</b> = <b>approved APH</b> ÷ county expected ({farmStats.countyExp != null ? farmStats.countyExp.toFixed(0) + " bu" : "—"}{farmStats.countyExpYear ? ", " + farmStats.countyExpYear : ""}); near 1.0 → the farm tracks its county, so SCO/ECO <i>area</i> coverage fits well; far off → basis risk cuts area-product value.
                {" "}Rows are split by practice, and the basis here is the same figure the wheat quote prints.
                {medCv != null && <span style={{ marginLeft:6, color:"#677167" }}>· median CV {medCv.toFixed(0)}% · median basis {medB != null ? medB.toFixed(2) : "—"}×</span>}
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", minWidth:480 }}>
                  <thead><tr style={{ textAlign:"left" }}>
                    <th style={th}>Farm</th>
                    <th style={{ ...th, textAlign:"center" }}>Yrs</th>
                    <th style={{ ...th, textAlign:"right" }}>APH</th>
                    <th style={{ ...th, textAlign:"right" }}>APH CV</th>
                    <th style={{ ...th, textAlign:"right" }}>Basis</th>
                  </tr></thead>
                  <tbody>
                    {farmStats.rows.map((r, i) => (
                      <tr key={r.fid + (r.org ? "|ORG" : "|CONV")} style={{ background: i % 2 ? "#fafcfa" : "#fff" }}>
                        <td style={{ ...td, fontWeight:700, color:"#1a2e1a" }}>{r.name}
                          {/* A farm insuring both sides is now TWO rows, so each has to say
                              which one it is - otherwise the table reads as a duplicate. */}
                          {r.org && <span style={{ marginLeft:6, fontSize:10.5, fontWeight:700, color:"#6b8f3f" }}>ORGANIC</span>}</td>
                        <td style={{ ...td, textAlign:"center", color:"#5c6f5c" }}>{r.n}</td>
                        <td style={{ ...td, textAlign:"right", fontWeight:700 }}>{r.aph.toFixed(1)}
                          {/* An APH the AIP never stated is the realised mean standing in for
                              one, and must not pass as an approved yield. */}
                          {r.aphSource !== "coverage" && (
                            <span style={{ marginLeft:5, fontSize:10, fontWeight:600, color:"#a5561d" }}
                                  title="No approved APH on file for this farm, county and practice — this is the mean of the realised yields, shown so the row is not blank.">est</span>
                          )}
                          {/* Grain bu/ac beside silage ton/ac. Averaging them is the Lismore
                              defect; the larger-acreage side is shown and flagged. */}
                          {r.unitSplit && (
                            <span style={{ marginLeft:5, fontSize:10, fontWeight:600, color:"#a5561d" }}
                                  title="This farm carries two APHs more than 3x apart in this county — a unit-of-measure split (bu/ac vs ton/ac). The larger-acreage side is shown; they are never averaged.">split</span>
                          )}</td>
                        <td style={{ ...td, textAlign:"right", fontWeight:800, color:cvColor(r.cv) }}>{r.cv != null ? r.cv.toFixed(0) + "%" : "—"}</td>
                        <td style={{ ...td, textAlign:"right", fontWeight:700, color:basisColor(r.basis) }}>{r.basis != null ? r.basis.toFixed(2) + "×" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        <div style={{ fontSize:11, color:"#677167", marginTop:14 }}>
          {/* ⚠ NO GEOGRAPHIC CLAIM HERE UNLESS IT IS DERIVED FROM THE DATA. This read
              "NY/PA/OH/MD" from the day the ingest carried four hardcoded states; on
              2026-09-17 it widened to every state FSA publishes and the label silently
              became false. Typing "48 states" would be the same defect one refresh
              later, so the coverage claim is gone rather than restated.
              The settlement note IS derived, because "2025 final" was also wrong: FSA
              settles a crop when its MYA goes final and that happens crop by crop —
              2025 wheat is final while corn and soybeans are still on a forecast MYA,
              so FSA has published no 2025 payment rate for two of the three crops this
              page shows. Reading it off `mya_status` means the line cannot go stale
              again the way the state list did. */}
          {(() => {
            const note = Object.keys(ref).map(Number).sort().map(Y => {
              const rows = CA_ORDER.map(c => (ref[Y] || {})[c]).filter(Boolean);
              if (!rows.length) return null;
              const fin = rows.filter(r => r.mya_status === "final").length;
              return Y + ": " + (fin === rows.length ? "final" : fin ? `${fin} of ${rows.length} crops final` : "in progress");
            }).filter(Boolean).join(" · ");
            return "ARC/PLC · USDA FSA program data" + (note ? ` (${note})` : "") + ".";
          })()} SCO/ECO live from the RMA RIRS report — corn/soy/wheat × NY/PA/OH/MD, 2025 finals + 2026 expected. ARC actual yield (FSA) and SCO/ECO actual yield (RMA area) come from different determinations — shown separately. SCO/ECO shows % of coverage triggered (county-level). Per-farm CV/basis from your book's production history (≥3 seasons); per-farm dollars come with the individual-farm tool.
        </div>
        </>}
      </>)}
      </>)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PORTAL HOME — the farmer's landing dashboard. A clean, at-a-glance summary of
// everything the agency protects for them: insured acres by crop, bushel + revenue
// guarantees, milk/livestock/PRF protection, local cash bids, and regional weather.
// Pure derivation from the RLS-scoped `farmers` prop (no cross-farmer risk) plus
// two anon-safe market reads (cash_bids_daily, wny_grid_weather_daily). Reuses the
// SAME bushel-guarantee math as PolicySectionList: liability ÷ effPrice, fallback
// APH × coverage × acres; revenue guarantee = liability. Kevin's ask: "real simple
// display of insured acres by crop, bushel guarantees, and revenue guarantees … good
// weather data, and local cash bids."
// ─────────────────────────────────────────────────────────────────────────────
// Fixed delivery-point coordinates for the cash-bid radius filter. Distance is a
// straight-line haversine from the farm; enough to separate local WNY elevators
// from the far Ohio Deerfield points.
const ELEVATOR_GEO = {
  wny_energy:           { lat: 43.2181, lon: -78.3867, name: "WNY Energy",            loc: "Medina, NY" },
  star_of_west:         { lat: 43.1017, lon: -77.8850, name: "Star of the West",      loc: "Churchville, NY" },
  adm_buffalo:          { lat: 42.8801, lon: -78.8785, name: "ADM Buffalo",           loc: "Buffalo, NY" },
  deerfield_deerfield:  { lat: 41.0339, lon: -81.0490, name: "Deerfield",             loc: "Deerfield, OH" },
  deerfield_kinsman:    { lat: 41.4570, lon: -80.5901, name: "Deerfield · Kinsman",   loc: "Kinsman, OH" },
  deerfield_louisville: { lat: 40.8376, lon: -81.2593, name: "Deerfield · Louisville",loc: "Louisville, OH" },
  deerfield_massillon:  { lat: 40.7967, lon: -81.5215, name: "Deerfield · Massillon", loc: "Massillon, OH" },
};
// Howlett Farms posts basis by delivery town — each is its own local bid point.
// The board spells the same town differently per commodity ("Avon & Lkville" for
// corn, "Avon/Lakevlle" for wheat), so match on a normalized town key, not the
// raw string.
const HOWLETT_TOWN = {
  avon:    { lat: 42.9120, lon: -77.7458, name: "Howlett · Avon" },
  leroy:   { lat: 42.9784, lon: -77.9803, name: "Howlett · LeRoy" },
  batavia: { lat: 43.0004, lon: -78.1875, name: "Howlett · Batavia" },
  geneva:  { lat: 42.8689, lon: -76.9780, name: "Howlett · Geneva" },
};
const howlettTown = (loc) => {
  const s = (loc || "").toLowerCase();
  if (s.includes("avon") || s.includes("lakev")) return "avon";
  if (s.includes("leroy") || s.includes("le roy") || s.includes("td ag")) return "leroy";
  if (s.includes("batavia")) return "batavia";
  if (s.includes("geneva")) return "geneva";
  return null; // pickup-only labels (P/U, Picked Up, Lima) have no fixed town
};
// Distance (mi) between two {lat,lon} points, guarding nulls — wraps the array
// haversineMi so the cash-bid radius filter can work off {lat,lon} objects.
function milesBetween(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  return haversineMi([a.lat, a.lon], [b.lat, b.lon]);
}

// ─────────────────────────────────────────────────────────────────────────────
// SILAS HOME — the safe front door. "What do I need to know today?"
//
// WHY THIS EXISTS: logging in used to land on the Dashboard, which is the agency
// command centre — acres, premium, liability, book metrics. That is the right
// screen and it is untouched; it is just the wrong FIRST screen when a customer
// or a prospect is standing beside you. Home is what you can open in any room.
//
// ⚠ THIS COMPONENT READS NO FARMER DATA, AND THAT IS THE WHOLE DESIGN.
// It fetches exactly one endpoint — /api/market?fn=home — which is itself under
// a no-book-data contract (see api/_home.js). Do NOT add an sbFetch of
// `farmers`, `policies`, `crop_coverage`, or anything else carrying a customer,
// however small the addition looks. Safe-by-construction beats a presentation
// toggle, because a toggle is a thing you forget to flip five minutes before a
// meeting. `tools/check-home-safe.js` fails the build if this drifts.
//
// EVERY NUMBER ON THIS PAGE CAN SHOW ITS WORK. Each fact carries `evidence[]`,
// and "Why am I seeing this?" prints the values with the TABLE and DATE behind
// them. There is no model in the loop today — the sentences are deterministic
// templates — and when phrasing is added it must pass assertNumeralsContained().
// ─────────────────────────────────────────────────────────────────────────────
// `allowedNav` is the caller's own reachable page list, and it is a SECURITY
// argument on the portal, not a tidy-up. This page's quick-access row links
// straight into All Customers, Claims, Field Mapper, Grain Marketing and
// Quoters. In the agent app that is correct — an admin reaches all of them. On
// the portal it is the one way a limited staff member could be handed a door
// out of the four pages they were granted, so the row is filtered to what the
// viewer already has. NULL means "no restriction" (the agent app), which keeps
// this a pure addition for the existing caller.
// ── THE HOME MASTHEAD ─────────────────────────────────────────────────────
// One component, both Home pages. It carries the date, a greeting, one sentence
// about the close built from the board's own numbers (boardSummaryLine — direction
// words only, no cause), an optional extra line, and — on the customer page only —
// the weather at the customer's own farm. `weather` is computed by the CALLER from
// the farm's nearest grid cell; the agent page passes none, so it carries no
// location at all.
// THE MASTHEAD FORECAST, ONE RULE FOR BOTH HOME PAGES. Grid daily rows (obs +
// forecast) for one 4-km cell become today plus the next seven days. The customer
// Home places the cell from the farm; the agent Home from the agent's own first
// saved Weather location. Two copies of this would drift in the one place a
// reader compares them side by side.
function homeForecastFromGrid(rows) {
  const d0 = new Date();
  const todayIso = d0.getFullYear() + "-" + String(d0.getMonth() + 1).padStart(2, "0") + "-" + String(d0.getDate()).padStart(2, "0");
  const C2F = (c) => Math.round(Number(c) * 9 / 5 + 32);
  const days = (rows || []).filter((r) => r.obs_date >= todayIso && r.tmax_c != null && r.tmin_c != null).slice(0, 7)
    .map((r) => ({ date: r.obs_date, hi: C2F(r.tmax_c), lo: C2F(r.tmin_c),
                   rain: r.precip_mm != null ? Number(r.precip_mm) / 25.4 : 0,
                   dow: new Date(r.obs_date + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }) }));
  if (!days.length) return null;
  const t = days[0].date === todayIso ? days[0] : null;
  return { days, today: t ? { hi: t.hi, lo: t.lo, rain: t.rain } : null,
           next7Rain: days.reduce((sum, d) => sum + (d.rain || 0), 0) };
}

// THE AGENT'S OWN FORECAST. The agent Home reads no book data, so it has no farm
// to place a forecast at — it used to show none at all. Each agent gets their OWN
// place (Kevin, 2026-10-17: the shop for him, Meisenzahl Farms for Mike, Daniele's
// own address), resolved from the viewer's own rows only, in this order:
//   1. portal_users.home_lat/home_lon/home_label — the same "your home" the
//      customer Home already honours (Daniele: Sauquoit; Mike: Meisenzahl Farms);
//   2. the viewer's own saved Weather location named "Shop" (Kevin);
//   3. nothing — no panel, never somebody else's place and never a guess.
// Both reads are scoped to the viewer (user_id = me; weather_locations is
// created_by = auth.uid()). Farther than 25 miles from any grid cell: no panel.
function useSavedSpotForecast() {
  const [w, setW] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const uid = getCurrentUserId();
      if (!uid) return;
      const pu = await sbFetch("portal_users", { select: "home_lat,home_lon,home_label", filters: ["user_id=eq." + uid], limit: 1 }).catch(() => []);
      const h = (pu || [])[0];
      let loc = h && h.home_lat != null && h.home_lon != null ? { name: h.home_label || null, lat: h.home_lat, lon: h.home_lon } : null;
      if (!loc) {
        const mine = await sbFetch("weather_locations", { select: "name,lat,lon", filters: ["name=ilike.shop"], limit: 1 }).catch(() => []);
        const own = (mine || [])[0];
        if (own && own.lat != null && own.lon != null) loc = own;
      }
      if (!loc) return;
      const at = { lat: Number(loc.lat), lon: Number(loc.lon) };
      const cells = await sbFetch("wny_grid_cells", { select: "id,lat,lon",
        filters: ["lat=gte." + (at.lat - 0.4), "lat=lte." + (at.lat + 0.4), "lon=gte." + (at.lon - 0.5), "lon=lte." + (at.lon + 0.5)] }).catch(() => []);
      let best = null;
      for (const c of (cells || [])) {
        if (c.lat == null) continue;
        const mi = milesBetween(at, { lat: Number(c.lat), lon: Number(c.lon) });
        if (!best || mi < best.mi) best = { id: c.id, mi };
      }
      if (!best || best.mi > 25) return;
      const d0 = new Date(); d0.setDate(d0.getDate() - 1);
      const since = d0.toISOString().slice(0, 10);
      const rows = await sbFetch("wny_grid_weather_daily", { select: "obs_date,tmax_c,tmin_c,precip_mm",
        filters: ["cell_id=eq." + best.id, "obs_date=gte." + since], order: "obs_date.asc,id.asc" }).catch(() => []);
      const f = homeForecastFromGrid(rows);
      if (alive && f) setW({ ...f, heading: "Weather", label: loc.name || null, lat: at.lat, lon: at.lon,
                             note: "Forecast for the 4-km grid cell nearest " + (loc.name ? loc.name : "your saved location") + "." });
    })();
    return () => { alive = false; };
  }, []);
  return w;
}

// THE HOME FORECAST, OPENED UP. Kevin, 2026-10-17: "I like the look of how simple it
// is now but I would like to be able to access more weather data" — rain chance,
// wind speed, wind direction. So the card stays as it is and a tap opens this under
// it. It loads only when opened (one Open-Meteo hourly read through op=fieldwork,
// the same hours the Weather page's Hours tab draws), so Home costs nothing extra
// until somebody asks.
//
// Direction is where the wind blows FROM (0° = from the north), which is how a
// forecast states it; the arrow points the way the air is GOING, the way a map
// arrow is read. A day's "prevailing" direction is the speed-weighted circular
// mean of its hours — a plain average of 350° and 10° is 180°, due south, which is
// the opposite of the truth.
const HOME_COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
function homeCompass(deg) {
  if (deg == null || !Number.isFinite(Number(deg))) return null;
  return HOME_COMPASS[Math.round((((Number(deg) % 360) + 360) % 360) / 22.5) % 16];
}
function homePrevailingDir(dirs, speeds) {
  let x = 0, y = 0;
  dirs.forEach((d, i) => {
    if (d == null) return;
    const w = Number(speeds[i]) > 0 ? Number(speeds[i]) : 0;
    const r = Number(d) * Math.PI / 180;
    x += w * Math.sin(r); y += w * Math.cos(r);
  });
  if (x === 0 && y === 0) return null;
  return ((Math.atan2(x, y) * 180 / Math.PI) + 360) % 360;
}

function HomeWeatherDetail({ lat, lon, date, isMobile = false, onMore = null }) {
  const [data, setData] = useState(null);   // null = loading, { error } on failure
  useEffect(() => {
    if (lat == null || lon == null) { setData({ error: "No location for this forecast." }); return; }
    let alive = true;
    fetch("/api/market?fn=precip&op=fieldwork&days=7&pts=" + encodeURIComponent(Number(lat).toFixed(4) + "," + Number(lon).toFixed(4)))
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        const pt = j && Array.isArray(j.locations) ? j.locations[0] : null;
        if (!pt || !pt.hourly) setData({ error: (pt && pt.error) || "Hourly forecast unavailable." });
        else setData(pt.hourly);
      })
      .catch(() => alive && setData({ error: "Hourly forecast unavailable." }));
    return () => { alive = false; };
  }, [lat, lon]);

  // minWidth 0 is load-bearing: without it the hourly strip's full width becomes
  // this panel's minimum, the card grows past the phone screen and the whole Home
  // page scrolls sideways (Kevin, 2026-10-17). The strip scrolls INSIDE instead.
  const box = { marginTop: 12, paddingTop: 12, borderTop: "1px solid #2c4a35", minWidth: 0, maxWidth: "100%" };
  if (!data) return <div style={{ ...box, fontSize: 12.5, color: "#a7bba6" }}>Loading the hourly forecast…</div>;
  if (data.error) return <div style={{ ...box, fontSize: 12.5, color: "#a7bba6" }}>{data.error}</div>;

  const idx = (data.t || []).map((t, i) => (String(t).slice(0, 10) === date ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return <div style={{ ...box, fontSize: 12.5, color: "#a7bba6" }}>No hourly forecast for that day.</div>;
  const pick = (k) => idx.map((i) => (data[k] ? data[k][i] : null));
  const pops = pick("pop").filter((v) => v != null);
  const winds = pick("wind"), gusts = pick("gust"), dirs = pick("dir"), rain = pick("precip");
  const maxPop = pops.length ? Math.max(...pops) : null;
  const total = rain.reduce((s, v) => s + (Number(v) || 0), 0);
  const maxWind = Math.max(...winds.map((v) => Number(v) || 0));
  const maxGust = Math.max(...gusts.map((v) => Number(v) || 0));
  const prevailing = homeCompass(homePrevailingDir(dirs, winds));
  const step = isMobile ? 3 : 2;
  const rows = idx.filter((i) => Number(String(data.t[i]).slice(11, 13)) % step === 0);
  const hourLabel = (t) => {
    const h = Number(String(t).slice(11, 13));
    return h === 0 ? "12a" : h < 12 ? h + "a" : h === 12 ? "12p" : (h - 12) + "p";
  };
  const stat = (label, value, sub) => (
    <div style={{ background: "#22402d", borderRadius: 12, padding: "9px 11px", minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a7bba6" }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11.5, color: "#b4c7b2", marginTop: 1 }}>{sub}</div> : null}
    </div>
  );
  return (
    <div style={box}>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0,1fr))" : "repeat(3, minmax(0,1fr))", gap: 8 }}>
        {stat("Chance of rain", maxPop != null ? maxPop + "%" : "—", total >= 0.01 ? total.toFixed(2) + "″ expected" : "under 0.01″")}
        {stat("Wind", maxWind ? "up to " + maxWind + " mph" : "calm", maxGust ? "gusts " + maxGust + " mph" : null)}
        {stat("Direction", prevailing ? "from " + prevailing : "—", "prevailing for the day")}
      </div>
      <div style={{ display: "flex", gap: isMobile ? 4 : 6, overflowX: "auto", marginTop: 12, paddingBottom: 2, maxWidth: "100%", minWidth: 0, WebkitOverflowScrolling: "touch" }}>
        {rows.map((i) => {
          const d = data.dir ? data.dir[i] : null;
          return (
            <div key={data.t[i]} style={{ flex: "0 0 auto", width: isMobile ? 46 : 50, textAlign: "center",
                                          background: "#1f3a29", borderRadius: 10, padding: "7px 2px" }}>
              <div style={{ fontSize: 11, color: "#a7bba6", fontWeight: 700 }}>{hourLabel(data.t[i])}</div>
              <div style={{ fontSize: 13.5, fontWeight: 800, marginTop: 3 }}>{data.temp && data.temp[i] != null ? data.temp[i] + "°" : "—"}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9fd0ef", marginTop: 3 }}>{data.pop && data.pop[i] != null ? data.pop[i] + "%" : "—"}</div>
              <div style={{ marginTop: 4, height: 14, display: "flex", justifyContent: "center", alignItems: "center" }}>
                {d != null ? (
                  <span title={"from " + homeCompass(d)} style={{ display: "inline-block", fontSize: 12, lineHeight: 1,
                        transform: "rotate(" + ((Number(d) + 180) % 360) + "deg)" }}>↑</span>
                ) : null}
              </div>
              <div style={{ fontSize: 11, color: "#dfe9dc" }}>{data.wind && data.wind[i] != null ? data.wind[i] : "—"}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 9, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: "#a7bba6" }}>Rows: temp · chance of rain · wind (mph), arrow shows where it’s blowing.</div>
        {onMore ? (
          <button onClick={onMore} style={{ border: "1px solid #3d6247", background: "transparent", color: "#a9d38a", borderRadius: 999,
                                            padding: "5px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
            Full weather →
          </button>
        ) : null}
      </div>
    </div>
  );
}

const CUSTOMER_TYPE_WORD = { GSG: "Grain", GSS: "Silage", LRK: "Light Red Kidney" };
function customerTypeLabel(type) {
  if (!type) return "";
  const k = String(type).trim().toUpperCase();
  return CUSTOMER_TYPE_WORD[k] || type;
}

function HomeMasthead({ isMobile = false, greeting = "", dateLong = "", summary = "", extra = "",
                        boardAsOf = null, weather = null, actions = null, onWeatherMore = null }) {
  const C2 = { pale: "#c7d8c3", lime: "#a9d38a", panel: "#1b3324", panelLine: "#2c4a35" };
  // The forecast opens on a tap (a day, or "More") and closes on the same tap.
  const [wxDay, setWxDay] = useState(null);
  const canDrill = !!(weather && weather.lat != null && weather.lon != null && weather.days && weather.days.length);
  return (
    <header style={{ background: "#12241a", color: "#f4f8f1", borderRadius: isMobile ? 18 : 22,
                     padding: isMobile ? "20px 18px 44px" : "30px 32px 56px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? 18 : 28, alignItems: "stretch",
                    justifyContent: "space-between" }}>
        <div style={{ flex: "1 1 380px", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12, fontWeight: 800,
                        letterSpacing: "0.14em", textTransform: "uppercase", color: C2.lime }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: C2.lime }} />
            {dateLong}
          </div>
          <h1 style={{ margin: 0, fontFamily: "'Lora',serif", fontWeight: 600,
                       fontSize: isMobile ? 32 : 46, lineHeight: 1.08, letterSpacing: "-0.01em" }}>
            {greeting}
          </h1>
          {(summary || extra) ? (
            <p style={{ margin: 0, fontSize: isMobile ? 15 : 17, lineHeight: 1.55, color: C2.pale, maxWidth: 620 }}>
              {summary}{summary && extra ? " " : ""}{extra}
            </p>
          ) : null}
          {boardAsOf ? (
            <div style={{ fontSize: 12, color: "#93ac91" }}>Board as of {fmtDate(boardAsOf)}</div>
          ) : null}
          {actions}
        </div>

        {weather ? (
          <section aria-label="Weather at your farm"
            style={{ flex: isMobile ? "1 1 100%" : "1 1 360px", minWidth: 0, maxWidth: isMobile ? "100%" : 540, boxSizing: "border-box", background: C2.panel,
                     border: "1px solid " + C2.panelLine, borderRadius: 18, padding: isMobile ? "16px 14px" : "18px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C2.lime }}>
                {weather.heading || "At your farm"}{weather.label ? " · " + weather.label : ""}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                {weather.next7Rain != null ? (
                  <div style={{ fontSize: 12, color: "#a7bba6" }}>Next 7 days · {weather.next7Rain.toFixed(2)}″ rain</div>
                ) : null}
                {canDrill ? (
                  <button onClick={() => setWxDay(wxDay ? null : weather.days[0].date)} aria-expanded={!!wxDay}
                    style={{ border: "none", background: "transparent", color: C2.lime, fontSize: 12, fontWeight: 800,
                             cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                    {wxDay ? "Less ▴" : "More ▾"}
                  </button>
                ) : null}
              </div>
            </div>
            {weather.today ? (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginTop: 10 }}>
                <div style={{ fontFamily: "'Lora',serif", fontSize: isMobile ? 46 : 58, fontWeight: 600, lineHeight: 1 }}>
                  {weather.today.hi}°
                </div>
                <div style={{ paddingBottom: 6 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>Today · low {weather.today.lo}°</div>
                  <div style={{ fontSize: 13, color: "#b4c7b2" }}>
                    {weather.today.rain != null ? weather.today.rain.toFixed(2) + "\" rain forecast" : ""}
                  </div>
                </div>
              </div>
            ) : null}
            {weather.days && weather.days.length ? (() => {
              const lo = Math.min(...weather.days.map((d) => d.lo)) - 2;
              const hi = Math.max(...weather.days.map((d) => d.hi)) + 2;
              const span = Math.max(1, hi - lo);
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(" + weather.days.length + ", minmax(0, 1fr))",
                              gap: isMobile ? 3 : 6, marginTop: 14 }}>
                  {weather.days.map((d, i) => (
                    <div key={d.date} role={canDrill ? "button" : undefined} tabIndex={canDrill ? 0 : undefined}
                      onClick={canDrill ? () => setWxDay(wxDay === d.date ? null : d.date) : undefined}
                      onKeyDown={canDrill ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setWxDay(wxDay === d.date ? null : d.date); } } : undefined}
                      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, cursor: canDrill ? "pointer" : "default",
                               padding: "7px 0", borderRadius: 12,
                               background: (wxDay ? wxDay === d.date : i === 0) ? "#2a4a34" : "transparent",
                               outline: wxDay === d.date ? "1px solid #a9d38a" : "none" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#dfe9dc" }}>{d.dow}</div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{d.hi}°</div>
                      <div style={{ position: "relative", width: 6, height: 48, borderRadius: 3, background: "#284331" }}>
                        <div style={{ position: "absolute", left: 0, right: 0, borderRadius: 3,
                                      bottom: ((d.lo - lo) / span * 100) + "%", height: ((d.hi - d.lo) / span * 100) + "%",
                                      background: "linear-gradient(#f2c66d, #7fb8d9)" }} />
                      </div>
                      <div style={{ fontSize: 12, color: "#a7bba6" }}>{d.lo}°</div>
                      {d.rain >= 0.05 ? (
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#9fd0ef" }}>{d.rain.toFixed(2)}″</div>
                      ) : <div style={{ fontSize: 11, color: "transparent" }}>.</div>}
                    </div>
                  ))}
                </div>
              );
            })() : null}
            {canDrill && wxDay ? (
              <HomeWeatherDetail lat={weather.lat} lon={weather.lon} date={wxDay} isMobile={isMobile} onMore={onWeatherMore} />
            ) : null}
            {weather.note ? <div style={{ fontSize: 11.5, color: "#a7bba6", marginTop: 10 }}>{weather.note}</div> : null}
          </section>
        ) : null}
      </div>
    </header>
  );
}

// ── "COMING UP" — dated events with a calendar badge ───────────────────────
// Takes the API's release calendar and, on the customer page, the grower's own
// RMA dates (src/home/insuranceDates.js). Every USDA report prints the EXACT day
// and time its publisher posted (NASS's calendar, AMS's release schedule) —
// Kevin, 2026-10-20: "This calendar should not show date ranges". A report whose
// next date is not posted yet is kept as a PENDING item and named under the
// list; it never gets a range or a guessed day.
function homeAgendaItems(calendar = [], insurance = []) {
  const fromCal = (calendar || []).map((ev) => (ev.exact ? {
    id: "cal-" + ev.id, date: ev.exact, daysAway: ev.daysAway, title: ev.label, mine: false,
    sub: [ev.timeET ? ev.timeET + " ET" : "", ev.agency || "USDA"].filter(Boolean).join(" · "),
    when: ev.daysAway === 0 ? "today" : ev.daysAway === 1 ? "tomorrow" : "in " + ev.daysAway + "d",
  } : {
    id: "cal-" + ev.id, date: null, pending: true, title: ev.label, mine: false,
    sub: ev.absentReason || "date not yet posted",
  }));
  const fromIns = (insurance || []).map((e) => ({
    id: e.id, date: e.date, daysAway: e.daysAway, title: e.title, mine: true,
    sub: e.sub + (e.varies ? " · earliest of your counties" : "") + " · RMA",
    when: e.daysAway === 0 ? "today" : "in " + e.daysAway + "d",
  }));
  return [...fromIns, ...fromCal].filter((x) => x.date || x.pending)
    .sort((a, b) => (!a.date || !b.date ? (a.date ? -1 : b.date ? 1 : 0)
      : a.date < b.date ? -1 : a.date > b.date ? 1 : (a.mine ? -1 : 1)));
}

function HomeAgenda({ items: allItems = [], isMobile = false, limit = 6, footnote = null }) {
  const { card, h2, eyebrow } = homeTheme(isMobile);
  // The reader's OWN dates are never crowded out by USDA reports: they take
  // their places first, the calendar fills what is left, and the list is then
  // put back in date order.
  // A report whose next date is not posted yet carries no date; it is named under
  // the list rather than given a place in it.
  const pending = allItems.filter((x) => !x.date && x.pending);
  const items = allItems.filter((x) => x.date);
  const mine = items.filter((x) => x.mine).slice(0, limit);
  const shown = [...mine, ...items.filter((x) => !x.mine).slice(0, Math.max(0, limit - mine.length))]
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : (a.mine ? -1 : 1)));
  return (
    <div style={card}>
      <div style={eyebrow}>Coming up</div>
      <h2 style={{ ...h2, marginBottom: 6 }}>On the calendar</h2>
      {shown.length ? shown.map((it) => {
        const d = new Date(String(it.date).slice(0, 10) + "T12:00:00Z");
        const mon = d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
        const day = d.toLocaleDateString("en-US", { day: "numeric", timeZone: "UTC" });
        return (
          <div key={it.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 0",
                                    borderTop: "1px solid " + LINE }}>
            <div style={{ flex: "0 0 50px", textAlign: "center", borderRadius: 12, padding: "5px 0",
                          background: it.mine ? "#12241a" : "#eef3ee", color: it.mine ? "#a9d38a" : INK }}>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>{mon}</div>
              <div style={{ fontFamily: "'Lora',serif", fontSize: 21, fontWeight: 700, lineHeight: 1.05 }}>{day}</div>
            </div>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <div style={{ color: INK, fontSize: 14.5, fontWeight: 700 }}>{it.title}</div>
              <div style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>{it.sub}</div>
            </div>
            <div style={{ color: it.mine ? "#1f6b34" : MUTED, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{it.when}</div>
          </div>
        );
      }) : <div style={{ color: MUTED, fontSize: 13 }}>Nothing scheduled in the next few weeks.</div>}
      {pending.length ? (
        <div style={{ color: MUTED, fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>
          {[...new Set(pending.map((p) => p.sub))].map((why) =>
            why.charAt(0).toUpperCase() + why.slice(1) + ": " +
            pending.filter((p) => p.sub === why).map((p) => p.title).join(", ") + ".").join(" ")}
        </div>
      ) : null}
      {footnote ? <div style={{ color: MUTED, fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>{footnote}</div> : null}
    </div>
  );
}

// ── SEASON SO FAR — rainfall and heat against normal, drawn ─────────────────
// Each bar runs to the larger of season-to-date and normal; the dark tick is
// normal. So "122% of normal" is a bar past its tick and "87%" one short of it,
// and the number beside it says the same thing in words.
function HomeSeasonCard({ isMobile = false, eyebrowText = "Your season", title = "Season so far",
                          rain = null, gdd = null, extras = [], footnote = null }) {
  const { card, h2, eyebrow } = homeTheme(isMobile);
  const meter = (now, normal, fill, track) => {
    const n = Number(now), m = Number(normal);
    if (!(n >= 0) || !(m > 0)) return null;
    const top = Math.max(n, m);
    return (
      <div style={{ position: "relative", height: 8, borderRadius: 4, background: track, marginTop: 10 }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: (n / top * 100) + "%", borderRadius: 4, background: fill }} />
        <div title="normal" style={{ position: "absolute", left: "calc(" + (m / top * 100) + "% - 1px)", top: -4, bottom: -4, width: 2, background: INK }} />
      </div>
    );
  };
  const tile = (t, bg, fg, fill, track) => t ? (
    <div style={{ background: bg, borderRadius: 14, padding: 14, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: fg }}>{t.label}</div>
      <div style={{ fontFamily: "'Lora',serif", fontSize: 27, fontWeight: 700, color: INK, marginTop: 2 }}>{t.value}</div>
      {meter(t.now, t.normal, fill, track)}
      <div style={{ fontSize: 13, fontWeight: 800, color: fg, marginTop: 8 }}>
        {t.pct == null ? "no comparison" : t.pct + "% of normal"}
      </div>
      {t.normalLabel ? <div style={{ fontSize: 11.5, color: MUTED }}>{t.normalLabel}</div> : null}
    </div>
  ) : null;
  return (
    <div style={card}>
      <div style={eyebrow}>{eyebrowText}</div>
      <h2 style={{ ...h2, marginBottom: 12 }}>{title}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
        {tile(rain, "#eef5fa", "#2c5d7c", "#3b82b5", "#d3e4ef")}
        {tile(gdd, "#fbf3e4", "#8a5a12", "#d08a1e", "#f1e1c2")}
      </div>
      {extras.length ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(" + extras.length + ", minmax(0, 1fr))", gap: 10, marginTop: 10 }}>
          {extras.map(([label, val, sub]) => (
            <div key={label} style={{ background: "#f4f8f4", borderRadius: 12, padding: "10px 12px" }}>
              <div style={{ fontSize: 12, color: MUTED, fontWeight: 700 }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: INK, marginTop: 2 }}>{val}</div>
              {sub ? <div style={{ fontSize: 11.5, color: MUTED }}>{sub}</div> : null}
            </div>
          ))}
        </div>
      ) : null}
      {footnote ? <div style={{ color: MUTED, fontSize: 11.5, marginTop: 10, lineHeight: 1.5 }}>{footnote}</div> : null}
    </div>
  );
}

function SilasHomeView({ isMobile = false, displayName = "", onNav = () => {}, allowedNav = null }) {
  const spotWeather = useSavedSpotForecast();
  const [perspKey, setPerspKey] = useState(null);  // which commodity the chart shows
  // Delivery month per commodity. Absent = new crop, which the API defaults to.
  const [months, setMonths] = useState({});
  // Diagnostics are collapsed by default — Home should read as finished. This is
  // deliberately NOT persisted: it resets on every visit so the engine
  // compartment is never left open in front of somebody.
  const [showHealth, setShowHealth] = useState(false);

  // ONE fetch, and the cards below are the SAME components the portal mounts —
  // see src/home/homeCards.jsx. The agent view asks for the full payload;
  // portal mode asks for it without the engine room.
  // The engine room (Data health, runtime absences) is served only to a verified
  // admin, so an admin sends the session token with the request. A staff member on
  // the portal "briefing" page, or Kevin previewing a farmer, sends none and gets
  // the public payload (blueprint B11). isCurrentUserAdmin() is the EFFECTIVE
  // identity, so it is false in preview.
  const { data, err, loading } = useHomeData({ months, authHeaders: isCurrentUserAdmin() ? getAuthHeaders : null });

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dateLong = now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  // "August 21" — the calendar date the perspective chart samples each year.
  const monthDayLabel = now.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  // The agent shell only knows the signed-in EMAIL, so `displayName` may be one.
  // An email is not a name: greeting "Good morning, kdkeenan10@gmail.com" is
  // worse than greeting nobody, and deriving a name from the local part
  // ("kdkeenan10" -> "Kdkeenan") invents one. Use it only when it reads as a
  // human name; otherwise greet without.
  const firstName = (() => {
    const s = String(displayName || "").trim();
    if (!s || s.includes("@")) return "";
    const first = s.split(/[\s,]+/)[0] || "";
    return /^[A-Za-z][A-Za-z'’-]{1,}$/.test(first) ? first : "";
  })();

  // Palette and card chrome come from the shared module so this page and the
  // portal cannot drift apart on colour or spacing.
  const { card, h2, eyebrow } = homeTheme(isMobile);
  const agoText = (isoStr) => {
    if (!isoStr) return "—";
    const t = Date.parse(isoStr.length <= 10 ? isoStr + "T12:00:00Z" : isoStr);
    if (isNaN(t)) return String(isoStr);
    const h = Math.round((Date.now() - t) / 3600000);
    if (h < 1) return "just now";
    if (h < 24) return h + "h ago";
    const d = Math.round(h / 24);
    return d + (d === 1 ? " day ago" : " days ago");
  };

  // ── quick access ───────────────────────────────────────────────────────────
  // Filtered to what the viewer can actually reach. A link to a page the nav
  // does not offer is a dead end at best; on the portal it is worse than that,
  // which is why allowedNav exists. Every entry here is a BOOK page except
  // "market" — so for a staff member granted only the open pages, this row
  // correctly collapses to Market Intelligence or disappears entirely.
  const QUICK_ALL = [
    { key: "book",    icon: "📇", label: "Customers" },
    { key: "grain",   icon: "🌽", label: "Grain" },
    { key: "quoters", icon: "🧮", label: "Quoters" },
    { key: "claims",  icon: "💰", label: "Claims" },
    { key: "fields",  icon: "🌾", label: "Field Mapper" },
    { key: "market",  icon: "📈", label: "Market Intel" },
    { key: "cashtargets", icon: "🎯", label: "Cash Targets" },
  ];
  const QUICK = allowedNav
    ? QUICK_ALL.filter((q) => allowedNav.includes(q.key))
    : QUICK_ALL.filter((q) => q.key !== "cashtargets");

  if (loading) {
    return <div style={{ textAlign: "center", padding: 60, color: MUTED }}>Loading today’s read…</div>;
  }

  const conditions = data && data.conditions;
  const calendar = (data && data.calendar) || [];
  // `health` is absent (not empty) when the payload was not served to an admin;
  // then the Data health control is not rendered at all, rather than "0/0 current".
  const hasHealth = !!(data && Array.isArray(data.health));
  const health = (data && data.health) || [];
  const absences = (data && data.absences) || [];
  const badHealth = health.filter((h) => h.state !== "ok");

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto" }}>
      {/* ── masthead + the price row it opens on ── */}
      <HomeMasthead isMobile={isMobile} greeting={greeting + (firstName ? ", " + firstName : "") + "."}
        dateLong={dateLong} summary={boardSummaryLine(data)} weather={spotWeather}
        onWeatherMore={() => onNav("rainwatch")}
        boardAsOf={data && data.markets ? data.markets.asOf : null} />
      <div style={{ position: "relative", marginTop: isMobile ? -30 : -38, padding: isMobile ? "0 8px" : "0 18px" }}>
        <HomeBoard data={data} isMobile={isMobile} onNav={onNav} parts="tiles" />
      </div>
      <div style={{ height: 20 }} />

      {err ? (
        <div style={{ ...card, borderColor: "#f0c9c4", background: "#fdf5f4", marginBottom: 16 }}>
          <strong style={{ color: "#a03a30" }}>Today’s data could not be assembled.</strong>
          <div style={{ color: MID, fontSize: 12.5, marginTop: 4 }}>{err}</div>
          <div style={{ color: MUTED, fontSize: 11.5, marginTop: 6 }}>
            Nothing below is estimated to fill the gap — a blank is the honest answer.
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
        <div style={{ flex: "999 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 20 }}>
          {/* "More market news" goes to the Markets hub only where this viewer's
              nav offers it — allowedNav is the portal/staff grant; the agent app
              always carries Markets. A link to a page the nav does not offer is a
              dead click, the same rule the quick-access row follows. */}
          <HomeBriefing data={data} isMobile={isMobile}
            onMore={(!allowedNav || allowedNav.includes("markets")) ? () => onNav("markets") : null} />
          <HomePerspective data={data} isMobile={isMobile} months={months} setMonths={setMonths}
            perspKey={perspKey} setPerspKey={setPerspKey} now={now} />
        </div>
        <div style={{ flex: "1 1 340px", minWidth: 0, display: "flex", flexDirection: "column", gap: 20 }}>
          <HomeBoard data={data} isMobile={isMobile} onNav={onNav} parts="side" />
          {conditions ? (
            <HomeSeasonCard isMobile={isMobile} eyebrowText="Growing conditions" title={conditions.region}
              rain={{ label: "Season rainfall", value: conditions.rain.now + '"', now: conditions.rain.now,
                      normal: conditions.rain.normal, pct: conditions.rain.pctOfNormal, normalLabel: conditions.rain.normal + '" normal' }}
              gdd={{ label: "Heat (GDD)", value: Number(conditions.gdd.now).toLocaleString(), now: conditions.gdd.now,
                     normal: conditions.gdd.normal, pct: conditions.gdd.pctOfNormal,
                     normalLabel: conditions.gdd.normal ? Number(conditions.gdd.normal).toLocaleString() + " normal" : null }}
              footnote={`Across ${Number(conditions.counties)} counties since ${conditions.seasonFrom}, against the same window in the prior ${conditions.normalYears} years. Observed through ${fmtDate(conditions.asOf)}.` +
                (conditions.next7Total != null ? ` Forecast for the next 7 days totals ${conditions.next7Total}".` : "")} />
          ) : (
            <div style={card}><div style={eyebrow}>Growing conditions</div><div style={{ color: MUTED }}>Conditions unavailable.</div></div>
          )}

          <HomeAgenda items={homeAgendaItems(calendar)} isMobile={isMobile} />
        </div>
      </div>

      {/* ── NO BRIEFS CARD ──
          The three AI market briefs are generated on demand, not on a cron, so
          the card showed a 45-day-old cattle read next to today’s board and
          spent its own badge explaining that. Removed 2026-08-22 (Kevin: "if
          briefs arent automatic, remove them"). Give them a daily cron first,
          then re-add — see the note in api/_home.js. */}
      {/* `identity` scopes the per-acre input program's browser store to the
          EFFECTIVE user — the previewed farmer while preview is on, the real
          session otherwise. localStorage is per-BROWSER, so without it an
          admin's cost program renders back at a farmer under their own login
          during preview, and two agents on one machine share one program.
          getCurrentUserId() is the JWT `sub`; a null means the card holds the
          rates for the session and writes nothing. */}
      <HomePurchasingPower data={data} isMobile={isMobile} identity={getCurrentUserId()} />

      {/* ── quick access ── */}
      {/* Hidden entirely when nothing survives the filter — an empty "Quick
          access" card reads as a broken page rather than as a narrow grant. */}
      {QUICK.length > 0 && <div style={{ ...card, marginTop: 16 }}>
        <div style={eyebrow}>Quick access</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {QUICK.map((q) => (
            <button key={q.key} onClick={() => onNav(q.key)}
              style={{ background: "#f7faf7", border: "1px solid " + LINE, borderRadius: 10,
                       padding: "10px 14px", cursor: "pointer", color: INK, fontWeight: 600,
                       fontSize: 12.5, display: "flex", alignItems: "center", gap: 7 }}>
              <span>{q.icon}</span>{q.label}
            </button>
          ))}
        </div>
      </div>}

      {/* ── DATA HEALTH — one line, and everything else behind it ──────────────
          The diagnostics were right and they were in the wrong room. A pipeline
          error ranked SECOND on the page one morning, above every market number,
          and "What this page does not know" listed the engine's limitations to
          whoever happened to be standing beside you. Both are genuinely valuable
          — they are what stops Silas pretending to know things it doesn't — so
          nothing was deleted: it moved behind a control that reads as finished
          rather than as under construction.

          The chip only turns amber when a feed is actually stale or erroring.
          sync_runs.status has SIX values and only `error` is a failure; the API
          owns that mapping (see api/_home.js), and this renders what it was told. */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 18 }}>
        <button onClick={() => setShowHealth((v) => !v)}
          style={{ background: badHealth.length ? "#fffbf5" : "#f7faf7",
                   border: "1px solid " + (badHealth.length ? "#f0dcc4" : LINE),
                   borderRadius: 20, padding: "5px 14px", cursor: "pointer",
                   color: badHealth.length ? "#a06a30" : MUTED, fontSize: 11.5, fontWeight: 600 }}>
          {hasHealth
            ? <>Data health: {health.length - badHealth.length}/{health.length} current{badHealth.length ? " ⚠" : ""}</>
            : "Intelligence coverage"}
          <span style={{ marginLeft: 6, opacity: 0.7 }}>{showHealth ? "▲" : "▼"}</span>
        </button>
      </div>

      {showHealth ? (
        <div style={{ ...card, marginTop: 10 }}>
          {hasHealth ? <div style={eyebrow}>Feeds</div> : null}
          {health.map((hh) => (
            <div key={hh.source} style={{ display: "flex", gap: 8, alignItems: "baseline",
                                          padding: "6px 0", borderBottom: "1px solid " + LINE, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: 8, flexShrink: 0,
                             background: hh.state === "ok" ? "#5c9e5c"
                                       : (hh.state === "stale" || hh.state === "degraded") ? "#d8a13a" : "#b3261e" }} />
              <strong style={{ color: INK, minWidth: 150 }}>{hh.label}</strong>
              <span style={{ color: MUTED }}>
                {hh.state === "ok" ? "current"
                  : hh.state === "degraded" ? "ran, but nothing new landed"
                  : hh.state}
                {hh.lastGood ? " · last good " + agoText(hh.lastGood) : ""}
                {hh.probe && hh.probe.indexOf("data") === 0 ? " · measured from the data, not a job log" : ""}
                {hh.sessionsBehind ? " · " + hh.sessionsBehind + " trading session" + (hh.sessionsBehind === 1 ? "" : "s") + " behind" : ""}
              </span>
              {hh.error ? <span style={{ color: "#a06a30", fontSize: 10.5 }}>{hh.error}</span> : null}
            </div>
          ))}

          <div style={{ ...eyebrow, marginTop: hasHealth ? 16 : 0 }}>Intelligence coverage</div>
          <div style={{ color: MUTED, fontSize: 11.5, marginBottom: 6 }}>
            Sources a card meant to read and could not. Named rather than omitted — a
            hole a reader fills from memory is worse than one that says what is missing.
          </div>
          {absences.length ? absences.map((a, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid " + LINE, fontSize: 11.5 }}>
              <strong style={{ color: INK }}>{a.source}</strong>
              <div style={{ color: MUTED, lineHeight: 1.5 }}>{a.reason}</div>
            </div>
          )) : <div style={{ color: MUTED, fontSize: 11.5 }}>Every source this page reads returned data.</div>}
        </div>
      ) : null}

      <div style={{ color: MUTED, fontSize: 10.5, textAlign: "center", margin: "18px 0 8px", lineHeight: 1.6 }}>
        Market and weather data only — no customer, acreage or premium figures appear on this page.
        <br />
        Headlines link to the outlet that published them; every figure is generated from a measured value.
      </div>
    </div>
  );
}


// ── ONE PORTAL LOGIN IS ONE OR MORE AUTHORIZED OPERATIONS ───────────────────
// A portal user's access to several farms is an ACCESS RELATIONSHIP. It is not
// permission to collapse those farms into one synthetic operation, and the
// difference is not cosmetic: measured on Greg Stewart's live account
// 2026-09-01, the Home rollup silently DISCARDED 1,230 of his own acres.
//
// `farmers` is split per policy_number, so several rows can belong to ONE
// insured. The operation identity is the insured NAME, normalised. My Coverage
// already resolved it that way with an inline `cvNorm`; this is that same rule
// at module scope so Home cannot grow a second interpretation of who the user
// is - the failure this repo records every time a rule is defined twice.
const portalOpKey = (f) => String((f && f.name) || "").trim().toLowerCase();

function portalOperations(farmers = []) {
  const m = new Map();
  for (const f of (farmers || [])) {
    const k = portalOpKey(f);
    if (!k) continue;   // an unnamed row cannot be attributed to an operation
    const g = m.get(k) || { key: k, name: f.name, rows: [], farmerIds: [] };
    g.rows.push(f);
    for (const id of [f.id, ...((f._allFarmerIds) || [])])
      if (id && g.farmerIds.indexOf(id) < 0) g.farmerIds.push(id);
    m.set(k, g);
  }
  return [...m.values()];
}

// ⚠ THE DEDUP KEY CARRIES THE OPERATION, AND THAT IS THE WHOLE FIX.
// This key was `county|crop|plan|type|practice`. It exists to stop ONE farm's
// field counting twice when two crop years are loaded, which it does correctly.
// Applied across TWO INSUREDS it collapsed their identical (county, crop, plan,
// type, practice) cells and kept exactly one - so the losing operation's acres
// vanished from a page the customer reads as his whole account.
//
// Fixing the CALLER (roll per operation) would have been enough today and would
// have left the same trap for the next caller. The identity now lives in the
// key, so passing mixed rows can no longer merge two operations at all.
function portalCoverageRollup(farmers, prices) {
    const byCrop = {};      // crop -> { acres, bu, unit, rev }
    const counties = new Set();
    let drpLiab = 0, drpCount = 0;
    let lrpInsured = 0, lrpHead = 0;
    let prfLiab = 0, prfCount = 0, prfAcres = 0;
    let supplementalRev = 0; // SCO/ECO bands

    // ── Dedup crop coverage across every policy card BEFORE summing. Key on the
    // coverage identity WITHOUT crop year, so the same field insured in two loaded
    // crop years (e.g. 2026 + an early 2027 wheat renewal) can't double-count.
    // Keep the row that actually carries a liability, then the higher crop year —
    // i.e. the active book. This is the guard against inflated guarantee totals. ──
    const covSeen = new Map();
    farmers.forEach(f => {
      const yr = f.cropYear || CURRENT_CROP_YEAR;
      (f.approvedYields || []).forEach(r => {
        const crop = (r.crop || "").toUpperCase();
        if (!crop) return;
        const key = `${portalOpKey(f)}|${(r.county || "").toUpperCase()}|${crop}|${r.plan || ""}|${r.type || ""}|${r.practice || ""}`;
        const cur = covSeen.get(key);
        const hasLiab = r.liability != null;
        if (!cur || (hasLiab && !cur.hasLiab) || (hasLiab === cur.hasLiab && yr > cur.yr)) {
          covSeen.set(key, { r, yr, hasLiab });
        }
      });
    });
    covSeen.forEach(({ r, yr }) => {
      const crop = (r.crop || "").toUpperCase();
      if (r.county) counties.add(normalizeCounty(r.county));
      // SCO / ECO / ARPI are area-plan supplements on top of the base guarantee —
      // keep them out of the per-crop base total (ARPI is an SCO plan; its plan
      // string is "ARPI" so it must be matched explicitly, not via "SCO").
      const isSupp = r.plan && (r.plan.includes("SCO") || r.plan.includes("ECO") || r.plan === "ARPI");
      if (isSupp) { supplementalRev += Number(r.liability) || 0; return; }
      // Resolve the coverage unit (bu / ton / lbs / cwt) up front so silage (corn
      // insured in TONS) can be split onto its own line — never merged into the
      // grain-corn bushel total.
      const isGuar = GUAR_UNIT_CROPS.has(crop) || FALL_CROPS.includes(crop);
      const pu = isGuar ? getCropPriceAndUnit(r, prices, yr) : null;
      const unit = pu ? pu.unit : null, price = pu ? pu.price : 0;
      const isSilage = unit === "ton" && crop === "CORN";
      const label = isSilage ? "CORN SILAGE" : crop;
      if (!byCrop[label]) byCrop[label] = { acres: 0, qty: 0, unit: isSilage ? "tons" : "bu", rev: 0 };
      byCrop[label].acres += r.acres ? Number(r.acres) : 0;
      byCrop[label].rev += Number(r.liability) || 0;
      if (isGuar) {
        // ONE method for every crop: production guarantee = liability ÷ elected
        // price. The elected price = projected price × price-election fraction —
        // that fraction is 100% for nearly all policies (corn $4.62, soy $11.09,
        // wheat $5.74, corn-silage $38.25/ton) BUT drops for CAT coverage (e.g.
        // 55%), where getCropPriceAndUnit still returns the full price. Applying
        // priceElectionPct is what keeps a CAT bushel guarantee from reading ~45%
        // low (verified vs Mehlenbacher's CAT SOI). Matches the AIP SOI to the unit.
        // APH×cov×acres is only a last-resort fallback (it overstates via simple-
        // average APH + the PP-acre reduction). Unit preserved (bu/tons/lbs/cwt) so
        // silage reads in tons and never merges with grain bushels.
        const pePct = Number(r.priceElectionPct ?? r.price_election_pct) > 0 ? Number(r.priceElectionPct ?? r.price_election_pct) : 1;
        const effPrice = (unit === "lbs" ? price * 0.01 : price) * pePct;
        let qty = null;
        // ⚠ Only a price map of this row's crop year may divide its liability (see the book list).
        const yearOk = r.cropYear == null || prices?._cropYear == null || Number(r.cropYear) === Number(prices._cropYear);
        if (yearOk && r.liability != null && effPrice > 0) qty = Number(r.liability) / effPrice;
        else if (r.acres && r.approvedYield && r.coverageLevel) qty = r.approvedYield * r.coverageLevel * Number(r.acres);
        const u = unit === "ton" ? "tons" : unit === "lbs" ? "lbs" : unit === "cwt" ? "cwt" : "bu";
        if (qty > 0) { byCrop[label].qty += qty; byCrop[label].unit = u; }
      }
    });

    // DRP / LRP / PRF — dedup endorsements/policies by identity so a grower with
    // the same coverage loaded under two crop-year cards isn't counted twice.
    const drpSeen = new Set(), lrpSeen = new Set(), prfSeen = new Set();
    farmers.forEach(f => {
      (f.drpPolicies || []).forEach(p => (p.endorsements || []).forEach(e => {
        const k = `${e.quarter || ""}|${e.endNum ?? ""}|${e.effectiveDate || ""}|${e.classIII ?? ""}`;
        if (drpSeen.has(k)) return; drpSeen.add(k);
        drpLiab += Number(e.liability ?? e.expRevGuar) || 0; drpCount++;
      }));
      (f.lrpPolicies || []).forEach(p => (p.endorsements || []).forEach(e => {
        const k = `${e.lineId ?? ""}|${e.salesDate || e.sales_date || ""}|${e.endDate || e.end_date || ""}|${e.head ?? e.numberOfHead ?? ""}`;
        if (lrpSeen.has(k)) return; lrpSeen.add(k);
        // insured_value is null on every LRP endorsement in the book — the covered
        // value lives in `liability`, so fall back to it (else the card blanks).
        lrpInsured += Number(e.insured_value ?? e.insuredValue ?? e.liability) || 0;
        lrpHead += Number(e.head ?? e.numberOfHead) || 0;
      }));
      // ⚠ ONE CROP YEAR ONLY, AND IT IS THE YEAR IN FORCE — see prfCoverageState(). Unscoped, this had BOTH
      // failure modes and neither was visible: where the two years carry
      // different policy numbers (NAU embeds the year) the dedupe key differs
      // and both years SUMMED — 16 farms; where they share one number (R&H
      // numbers carry no year) the key collided and one year was dropped by
      // load order — 13 farms, so Oakfield Alabama read $894,956 or $1,070,643
      // depending on which row arrived first. Measured 2026-09-03.
      prfCoverageState(f.prfPolicies, prfCoverageYear()).policies.forEach(p => {
        const k = p.policyNumber || p.id || `${p.county || ""}|${p.totalLiability || ""}`;
        if (prfSeen.has(k)) return; prfSeen.add(k);
        prfLiab += Number(p.totalLiability) || (p.intervals || []).reduce((s, iv) => s + (Number(iv.liability) || 0), 0);
        prfCount++;
        // Acres: PRF re-insures the SAME ground in every elected interval, so they
        // sum WITHIN an interval and never ACROSS. Sum the unit lines inside each
        // (grid, county, interval) -> take the max across intervals per block ->
        // sum the blocks. Same shape as intervalAcresSum in PRFView and
        // prfTotalAcres() in both syncs. The stored total_acres wins when it is at
        // least the block sum (it is the SOI's own figure); the block sum is the
        // fallback for a policy that never got one. Measured 2026-08-20: the two
        // agree to the cent on all four CY2026 Michigan PRF blocks.
        const byBlockIv = {};
        (p.intervals || []).forEach(iv => {
          const cty = (iv.county || p.county || "").toUpperCase();
          const key = `${iv.gridId || iv.grid_id || "?"}|${cty}|${iv.intervalCode || iv.interval_code || "?"}`;
          byBlockIv[key] = (byBlockIv[key] || 0) + (Number(iv.insuredAcres ?? iv.insured_acres) || 0);
          if (iv.county) counties.add(normalizeCounty(iv.county));
        });
        const byBlock = {};
        Object.entries(byBlockIv).forEach(([bkey, bacres]) => {
          const b = bkey.split("|").slice(0, 2).join("|");
          byBlock[b] = Math.max(byBlock[b] || 0, bacres);
        });
        const blockAcres = Object.values(byBlock).reduce((s, a) => s + a, 0);
        const storedAcres = Number(p.totalAcres) || 0;
        prfAcres += storedAcres >= blockAcres ? storedAcres : blockAcres;
        if (p.county) counties.add(normalizeCounty(p.county));
      });
    });

    // PRF gets its own line beside the crops. RMA insures it AS a crop — PASTURE,
    // RANGELAND, FORAGE, crop code 0088 — so its acres belong in the same table the
    // grower reads for corn and soybeans. It settles on a RAINFALL INDEX, never on
    // production, so the guarantee column stays blank rather than carrying an
    // invented bushel figure. Hay vs grazing is not stored on prf_policies, so the
    // line takes RMA's own crop name and claims neither.
    if (prfCount > 0 && (prfAcres > 0 || prfLiab > 0)) {
      // Add rather than assign: crop_coverage carries no PRF crop name today
      // (0 rows book-wide, measured 2026-08-20 — PRF lives in its own silo), but a
      // bare assignment would silently drop that line if one ever appeared.
      const prfKey = "PASTURE, RANGELAND, FORAGE";
      const prior = byCrop[prfKey] || { acres: 0, qty: 0, unit: "", rev: 0 };
      byCrop[prfKey] = {
        ...prior, acres: prior.acres + prfAcres, rev: prior.rev + prfLiab,
        short: "PASTURE", color: "#4a7fa5", isPrf: true,
        note: `Rainfall index · ${prfCount} PRF ${prfCount === 1 ? "policy" : "policies"}`,
      };
    }

    const cropRows = Object.entries(byCrop)
      .map(([crop, v]) => ({ crop, ...v }))
      // Crops rank by size; PRF pins to the bottom — its dollars are a rainfall-index
      // protection amount, not a crop revenue guarantee, so it shouldn't shuffle into
      // the middle of the crop comparison.
      .sort((a, b) => (a.isPrf ? 1 : 0) - (b.isPrf ? 1 : 0) || b.rev - a.rev || b.acres - a.acres);
    const totalAcres = cropRows.reduce((s, r) => s + r.acres, 0);
    const totalCropRev = cropRows.reduce((s, r) => s + r.rev, 0);
    // prfLiab already sits inside totalCropRev via the PRF row above — adding it
    // again here would double it against the hero "Revenue Protected".
    const totalRev = totalCropRev + supplementalRev + drpLiab + lrpInsured;
    return { cropRows, totalAcres, totalCropRev, supplementalRev, drpLiab, drpCount, lrpInsured, lrpHead, prfLiab, prfCount, prfAcres, counties: counties.size, totalRev };
}

function PortalHomeView({ farmers = [], prices = {}, isMobile = false, displayName = "", homeLat = null, homeLon = null, homeLabel = null, onSelectOperation = null, onOpenMarkets = null }) {
  // ── THE MARKET HALF IS THE SAME COMPONENTS THE AGENT HOME MOUNTS ──────────
  // src/home/homeCards.jsx, one definition. `portal: true` asks the API for the
  // payload WITHOUT the engine room — pipeline health and runtime absences are
  // dropped server-side, so a customer's browser never receives our sync source
  // names or a PostgREST error string. Nothing in those cards reads farmer data;
  // the customer's own numbers stay in this file, above them.
  const [perspKey, setPerspKey] = useState(null);
  const [months, setMonths] = useState({});
  // ⚠ THE STATES THIS READER ACTUALLY FARMS, so a regional fact cannot be shown to
  // somebody it is not about. Chris Dawson farms Michigan, Ohio and Indiana and was
  // shown "NY drought severity" — Kevin, 2026-09-07: "A customer in Michigan should
  // not being seeing NY drought info…"
  //
  // Taken from the POLICY state on this viewer's own operations, not from
  // `farmers.state`, which is NULL on all eight of his rows (the portal farmer card
  // resolves it from the policy — see the note on hubDefaultState). This is the
  // client telling the endpoint what it already knows about itself; the endpoint
  // never looks the reader up, so Home's readsFarmerData contract is untouched.
  const readerStates = useMemo(() => [...new Set((farmers || [])
    .map((f) => String(f.state || "").toUpperCase()).filter(Boolean))].sort(), [farmers]);
  const { data: mkt } = useHomeData({ months, portal: true, states: readerStates });
  const [bids, setBids] = useState([]);
  const [wx, setWx] = useState([]);            // nearest-cell daily weather (obs + forecast)
  const [clim, setClim] = useState([]);        // nearest-cell 30-yr normals (by day_of_year)
  const [cells, setCells] = useState([]);      // wny_grid_cells (id, lat, lon, county) — origin + nearest-cell
  const [farmGeo, setFarmGeo] = useState([]);  // this user's farm lat/lon/county (radius origin)
  const [apiWx, setApiWx] = useState(null);    // on-demand Open-Meteo season weather for farms OUTSIDE the WNY grid
  const [board, setBoard] = useState([]);      // grain_basis_board (Howlett posted basis)
  const [fut, setFut] = useState([]);          // futures_settles_daily (to turn basis → cash)
  const [radiusMi, setRadiusMi] = useState(30); // cash-bid radius selector

  // Local cash bids (anon-safe market data) — last 21 days, WNY elevators.
  useEffect(() => {
    let alive = true;
    const since = new Date(Date.now() - 21 * 86400000).toISOString().slice(0, 10);
    Promise.all([
      sbFetch("cash_bids_daily", { filters: [`bid_date=gte.${since}`], order: "bid_date.desc" }).catch(() => []),
      // Howlett's posted bids only while the Cash Board switch allows them (see
      // howlettBidsVisibleP) — the switch exists for privacy, and this card read
      // the sheet directly, so a grower saw Howlett bids with the board OFF.
      howlettBidsVisibleP().then((ok) => ok ? sbFetch("grain_basis_board", { select: "commodity,location,term,cme_contract,ship_month,ship_sort,basis,updated_at", order: "ship_sort.asc,id.asc" }) : []).catch(() => []),
      sbFetch("futures_settles_daily", { select: "commodity,contract_date,settle_price,settle_date", filters: ["contract_date=not.is.null"], order: "settle_date.desc,id.asc" }).catch(() => []),
    ]).then(([b, bb, f]) => { if (!alive) return; setBids(b || []); setBoard(bb || []); setFut(f || []); });
    return () => { alive = false; };
  }, []);

  const anyNY = useMemo(
    () => farmers.some(f => (f.state || "").toUpperCase().includes("NY")) || farmGeo.some(f => f.lat != null) || farmers.length === 0,
    [farmers, farmGeo]
  );
  // This user's farm coordinates (radius origin) + the WNY grid-cell index (for the
  // nearest-cell growing-season weather + county-centroid fallback origin).
  useEffect(() => {
    let alive = true;
    const ids = farmers.map(f => f.id).filter(Boolean);
    // NOTE: farmers has NO `county` column (see tools/schema.json). Selecting it made
    // PostgREST 400 the whole request, the .catch swallowed it, and farmGeo stayed
    // empty — so the cash-bid radius silently never had a farm origin and always fell
    // through to "show all bids". County comes from coverage instead, below.
    if (ids.length) sbFetch("farmers", { select: "id,lat,lon,city,zip", filters: [`id=in.(${ids.join(",")})`] }).then(r => { if (alive) setFarmGeo(r || []); }).catch(() => {});
    sbFetch("wny_grid_cells", { select: "id,lat,lon,county_name,state_fips" }).then(r => { if (alive) setCells(r || []); }).catch(() => {});
    return () => { alive = false; };
  }, [farmers]);

  // ── Radius origin: farm lat/lon if we have it, else the centroid of the user's
  // primary county (from the grid-cell index), else null (→ show all bids). ──
  const origin = useMemo(() => {
    // A portal user's own home wins (esp. agents like Daniele, who have no farm) —
    // so the weather + cash-bid radius center on THEM, not their first customer.
    if (homeLat != null && homeLon != null) return { lat: Number(homeLat), lon: Number(homeLon), src: homeLabel || "your home" };
    const g = farmGeo.find(f => f.lat != null && f.lon != null);
    if (g) return { lat: Number(g.lat), lon: Number(g.lon), src: g.city || "your farm" };
    // county fallback
    // farmGeo carries no county (the farmers table has none), so the grower's county
    // comes from their coverage rows.
    const county = (farmers.flatMap(f => (f.approvedYields || []).map(r => r.county)).find(Boolean) || "").toUpperCase();
    if (county && cells.length) {
      // NARROWED TO THE GROWER'S OWN STATE. The grid covers New York and, since
      // 2026-09-02, Pennsylvania — and the two share SIXTEEN county names. An
      // unscoped name match averages NY Erie's cells together with PA Erie's and
      // puts the radius origin in the middle of Lake Erie: not a worse version of
      // the old answer, a NEW error in a county that used to be right.
      // farmer.state is first-wins across a grower's policies (a farm may span
      // states — see farmStates), so it is used where it says anything at all, and
      // where it is absent the match is REFUSED rather than resolved across states.
      // Falling through here shows ALL bids — a visibly wider list beats a quietly
      // wrong centre.
      const stF = STATE_FIPS[String((farmers.find(f => f.state) || {}).state || "").toUpperCase()] || null;
      const named = cells.filter(c => (c.county_name || "").toUpperCase() === county && c.lat != null);
      const nStates = new Set(named.map(c => String(c.state_fips || "")));
      const inCo = stF ? named.filter(c => String(c.state_fips || "") === stF) : (nStates.size === 1 ? named : []);
      if (inCo.length) return { lat: inCo.reduce((s, c) => s + Number(c.lat), 0) / inCo.length, lon: inCo.reduce((s, c) => s + Number(c.lon), 0) / inCo.length, src: `${county.charAt(0) + county.slice(1).toLowerCase()} County` };
    }
    return null;
  }, [farmGeo, farmers, cells, homeLat, homeLon, homeLabel]);

  // Nearest grid cell to the origin. If it's more than ~25 mi away the farm sits
  // OUTSIDE the WNY grid (central-NY / Michigan books) — use live Open-Meteo weather
  // for the farm's actual point instead of a far-off edge cell.
  const nearestCell = useMemo(() => {
    if (!origin || !cells.length) return null;
    let best = null, bd = Infinity;
    for (const c of cells) { if (c.lat == null) continue; const d = milesBetween(origin, { lat: Number(c.lat), lon: Number(c.lon) }); if (d < bd) { bd = d; best = { id: c.id, mi: d }; } }
    return best;
  }, [origin, cells]);
  const outOfGrid = !!(origin && nearestCell && nearestCell.mi > 25);
  const cellId = (nearestCell && !outOfGrid) ? nearestCell.id : null;

  // Out-of-grid → fetch on-demand season weather for the farm's lat/lon.
  useEffect(() => {
    if (!outOfGrid || !origin) { setApiWx(null); return; }
    let alive = true;
    fetch(`/api/market?fn=season-weather&lat=${origin.lat}&lon=${origin.lon}`)
      .then(r => r.json()).then(j => { if (alive && j && j.ok) setApiWx(j); }).catch(() => {});
    return () => { alive = false; };
  }, [outOfGrid, origin]);

  // Growing-season weather for that cell: actuals + forecast since Mar 1, plus the
  // 30-yr normal (climatology) to show rain & heat vs normal.
  useEffect(() => {
    if (!cellId) { setWx([]); setClim([]); return; }
    let alive = true;
    const since = `${new Date().getUTCFullYear()}-03-01`;
    Promise.all([
      sbFetch("wny_grid_weather_daily", { select: "obs_date,tmax_c,tmin_c,precip_mm,precip_gridmet_mm,precip_mrms_mm,source,is_forecast", filters: [`cell_id=eq.${cellId}`, `obs_date=gte.${since}`], order: "obs_date.asc" }).catch(() => []),
      sbFetch("wny_grid_climatology", { select: "day_of_year,gdd_f,precip_mm", filters: [`cell_id=eq.${cellId}`] }).catch(() => []),
    ]).then(([w, c]) => { if (!alive) return; setWx(w || []); setClim(c || []); });
    return () => { alive = false; };
  }, [cellId]);

  // ── THIS GROWER'S OWN RMA DATES ─────────────────────────────────────────────
  // WHERE they insure comes from policy_fields (state_fips + county_fips + RMA
  // commodity on every insured field — a county NAME is ambiguous across NY/PA);
  // WHEN comes from rma_dates, RMA's own ADM (sql/086). Neither is typed here.
  // Read under the customer's own session: policy_fields is farmer-scoped by RLS,
  // rma_dates is public actuarial data.
  const [insPlaces, setInsPlaces] = useState([]);
  const [insDates, setInsDates] = useState([]);
  const farmerIdKey = useMemo(() => [...new Set((farmers || [])
    .flatMap((f) => [f.id, ...((f._allFarmerIds) || [])]).filter(Boolean))].sort().join(","), [farmers]);
  useEffect(() => {
    let alive = true;
    if (!farmerIdKey) { setInsPlaces([]); setInsDates([]); return undefined; }
    const cy = CURRENT_CROP_YEAR;
    sbFetch("policy_fields", { select: "state_fips,county_fips,rma_crop_code",
      filters: [`farmer_id=in.(${farmerIdKey})`, `crop_year=gte.${cy - 1}`, "rma_crop_code=not.is.null"] })
      .then((rows) => {
        if (!alive) return null;
        const seen = new Set(); const places = [];
        (rows || []).forEach((r) => {
          const k = `${r.state_fips}|${r.county_fips}|${r.rma_crop_code}`;
          if (!seen.has(k)) { seen.add(k); places.push(r); }
        });
        setInsPlaces(places);
        if (!places.length) { setInsDates([]); return null; }
        const uniq = (k) => [...new Set(places.map((p) => String(p[k] || "")).filter(Boolean))];
        return sbFetch("rma_dates", {
          select: "crop_year,commodity_code,state_code,county_code,insurance_plan_code,sales_closing_date,acreage_reporting_date,final_planting_date",
          filters: [`crop_year=in.(${cy},${cy + 1})`, `commodity_code=in.(${uniq("rma_crop_code").join(",")})`,
                    `state_code=in.(${uniq("state_fips").join(",")})`, `county_code=in.(${uniq("county_fips").join(",")})`,
                    "insurance_plan_code=in.(01,02,03,90)"] })
          .then((d) => { if (alive) setInsDates(d || []); });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [farmerIdKey]);
  const insEvents = useMemo(() => {
    const cropName = {};
    Object.entries(RMA_ADM_CROPS).forEach(([crop, v]) => {
      if (!cropName[v.code]) cropName[v.code] = crop.charAt(0) + crop.slice(1).toLowerCase();
    });
    cropName["0088"] = "Pasture (PRF)";
    return upcomingInsuranceDates({ places: insPlaces, dates: insDates, today: new Date(), cropName });
  }, [insPlaces, insDates]);

  // ── The 7-day strip in the masthead, from the farm's nearest grid cell ──────
  const mastWeather = useMemo(() => {
    // "your farm" is the fallback NAME when the farm has no city — printing it
    // after "At your farm" says nothing, so it is dropped.
    const label = origin && origin.src && !/^your (farm|home)$/i.test(origin.src) ? origin.src : null;
    if (!outOfGrid && wx.length) {
      const f = homeForecastFromGrid(wx);
      return f ? { ...f, label, lat: origin ? origin.lat : null, lon: origin ? origin.lon : null, note: "Forecast for the 4-km grid cell nearest your farm." } : null;
    }
    if (outOfGrid && apiWx && apiWx.forecast7) {
      return { label, days: [], today: null, next7Rain: apiWx.forecast7.rainIn ?? null,
               note: apiWx.forecast7.avgHighF != null ? `Average high over the next 7 days about ${Math.round(apiWx.forecast7.avgHighF)}°F.` : null };
    }
    return null;
  }, [wx, outOfGrid, apiWx, origin]);

  // ── Coverage rollup across all of the user's farms ──
  // Per-operation rollups. `portalCoverageRollup` is shared and keyed on the
  // operation, so an account total is the SUM of operations rather than a
  // re-aggregation that could merge them again.
  const operations = useMemo(() => portalOperations(farmers), [farmers]);
  const opRolls = useMemo(
    () => operations.map((op) => ({ op, roll: portalCoverageRollup(op.rows, prices) })),
    [operations, prices]);
  const roll = useMemo(() => portalCoverageRollup(farmers, prices), [farmers, prices]);
  const multiOp = operations.length > 1;
  // THE ACCOUNT TOTAL IS THE SUM OF THE OPERATIONS, never a re-aggregation of the
  // raw rows - re-rolling could merge two operations again, which is the defect.
  const acctAcres = useMemo(() => opRolls.reduce((s, x) => s + (x.roll.totalAcres || 0), 0), [opRolls]);
  // Crops are counted as DISTINCT crops across operations, not summed: two
  // operations both growing corn is one crop on the account, two on the cards.
  const acctCrops = useMemo(() => new Set(opRolls.flatMap(x => x.roll.cropRows.map(r => r.crop))).size, [opRolls]);

  // ── Local cash bids per commodity, per delivery point, with distance from the
  // farm (for the radius filter). WNY elevators come from cash_bids_daily; Howlett
  // is computed from its posted basis sheet (grain_basis_board) + the matching
  // futures settle, one bid per delivery town. ──
  const cashByCommodity = useMemo(() => {
    const MON = { JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6, JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 };
    const periodOrd = (p) => {
      const s = (p || "").toUpperCase();
      if (!s || s.includes("NEAR") || s.includes("CASH") || s.includes("SPOT")) return 0;
      const ym = s.match(/(\d{2})$/); const yr = ym ? 2000 + parseInt(ym[1], 10) : 9999;
      let mon = 1; for (const k in MON) { if (s.startsWith(k)) { mon = MON[k]; break; } }
      return yr * 100 + mon;
    };
    const norm = (c) => { const s = (c || "").toLowerCase(); if (s.startsWith("corn")) return "CORN"; if (s.startsWith("soy")) return "SOYBEANS"; if (s.startsWith("wheat")) return "WHEAT"; return null; };
    const dist = (geo) => geo ? milesBetween(origin, geo) : null;
    // Drop delivery slots that have already rolled past — keeps the front month
    // current on its own without waiting on the scraper to stop posting them.
    // (0 = nearby/cash/spot, always kept; unknowns rank far-future, kept.)
    const nowOrd = new Date().getUTCFullYear() * 100 + (new Date().getUTCMonth() + 1);
    const isPast = (p) => { const o = periodOrd(p); return o !== 0 && o < nowOrd; };
    const byCom = {};
    // 1) Scraped WNY elevators — most-recent, nearest-CURRENT-delivery bid per source.
    const best = {};
    bids.forEach(r => {
      const com = norm(r.commodity); if (!com || r.cash_price == null || isPast(r.delivery_period)) return;
      const k = `${r.source}|${com}`; const cur = best[k];
      if (!cur || r.bid_date > cur.bid_date || (r.bid_date === cur.bid_date && periodOrd(r.delivery_period) < periodOrd(cur.delivery_period))) best[k] = r;
    });
    Object.values(best).forEach(r => {
      const com = norm(r.commodity); const geo = ELEVATOR_GEO[r.source];
      (byCom[com] ||= []).push({ source: r.source, name: geo?.name || (r.source || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()), loc: geo?.loc || r.delivery_location || "", price: Number(r.cash_price), period: r.delivery_period, date: r.bid_date, dist: dist(geo) });
    });
    // 2) Howlett — cash = matching futures settle + posted basis (front delivered slot per town).
    const futBy = {};
    fut.forEach(r => { if (r.settle_price == null || !r.contract_date) return; const k = `${r.commodity}|${String(r.contract_date).slice(0, 7)}`; if (!futBy[k] || r.settle_date > futBy[k].date) futBy[k] = { price: Number(r.settle_price), date: r.settle_date }; });
    const cmeToYm = (cme) => { const m = String(cme || "").toUpperCase().match(/^([FGHJKMNQUVXZ])(\d{2})$/); if (!m) return null; return `20${m[2]}-${String(_FUTURES_CODE_TO_MONTH_NUM[m[1]]).padStart(2, "0")}`; };
    const boardByTownCom = {}; // `${town}|${com}` -> delivered rows with a basis
    board.forEach(r => {
      if ((r.term || "") !== "delivered" || r.basis == null) return;
      const com = (r.commodity || "").toLowerCase();
      if (!["corn", "soybeans", "wheat"].includes(com)) return;
      const town = howlettTown(r.location); if (!town) return;
      (boardByTownCom[`${town}|${com}`] ||= []).push(r);
    });
    Object.entries(boardByTownCom).forEach(([key, rows]) => {
      const [town, com] = key.split("|");
      rows.sort((a, b) => (a.ship_sort ?? 99) - (b.ship_sort ?? 99));
      // nearest slot whose futures contract actually has a settle (a 2027-harvest
      // wheat slot with no settle yet is skipped in favor of the nearby month).
      let chosen = null, price = null;
      for (const r of rows) { const ym = cmeToYm(r.cme_contract); if (!ym) continue; const f = futBy[`${com}|${ym}`]; if (!f) continue; chosen = r; price = +(f.price + Number(r.basis)).toFixed(2); break; }
      if (!chosen) return;
      const geo = HOWLETT_TOWN[town];
      (byCom[com.toUpperCase()] ||= []).push({ source: `howlett_${town}`, name: geo.name, loc: "Howlett Farms", price, period: chosen.ship_month, date: (chosen.updated_at || "").slice(0, 10), dist: dist(geo), howlett: true });
    });
    Object.values(byCom).forEach(a => a.sort((x, y) => y.price - x.price));
    return byCom;
  }, [bids, board, fut, origin]);

  // ── Growing-season review for the farm's nearest grid cell: rain + GDD to date
  // vs the 30-yr normal, plus a 7-day forecast. Replaces the old rainfall strip. ──
  const season = useMemo(() => {
    // Out-of-grid farms use the live Open-Meteo season weather (same shape as below).
    if (outOfGrid) {
      if (!apiWx) return null;
      return {
        rain: apiWx.rainToDateIn, gdd: apiWx.gddToDate,
        normRain: apiWx.rainNormalIn, normGdd: apiWx.gddNormal,
        rainPct: apiWx.rainPctNormal, gddPct: apiWx.gddPctNormal,
        last7: apiWx.last7RainIn, next7: apiWx.forecast7?.rainIn,
        next7High: apiWx.forecast7?.avgHighF, daysSinceRain: apiWx.daysSinceRain,
        lastObs: apiWx.asOf,
      };
    }
    if (!wx.length) return null;
    const C2F = (c) => c == null ? null : (Number(c) * 9 / 5 + 32);
    const doy = (d) => { const dt = new Date(d + "T00:00:00Z"); return Math.floor((dt - new Date(Date.UTC(dt.getUTCFullYear(), 0, 0))) / 86400000); };
    const climByDoy = {}; clim.forEach(c => { climByDoy[c.day_of_year] = c; });
    let rain = 0, gdd = 0, nRain = 0, nGdd = 0, lastObs = null, lastGm = null, daysSinceRain = null;
    const fcRain = []; const fcHigh = [];
    // The day's rain for "last 7 days" and "days since rain": gridMET where it has
    // published, radar for the newest days it hasn't. Neither is set against a normal.
    const dayIn = (r) => { const g = WX.gridmetPrecipMm(r); const v = g != null ? g : WX.radarPrecipMm(r); return v == null ? null : v / 25.4; };
    wx.forEach(r => {
      if (r.is_forecast) {
        if (r.precip_mm != null) fcRain.push(Number(r.precip_mm) / 25.4);
        const hi = C2F(r.tmax_c); if (hi != null) fcHigh.push(hi);
        return;
      }
      lastObs = r.obs_date;
      const inch = dayIn(r);
      if (inch != null) { if (inch >= 0.1) daysSinceRain = 0; else if (daysSinceRain != null) daysSinceRain++; }
      // GDD base 50°F, cap 86 (corn)
      const hi = Math.min(86, C2F(r.tmax_c) ?? 50), lo = Math.max(50, C2F(r.tmin_c) ?? 50);
      const g = Math.max(0, (hi + lo) / 2 - 50); gdd += g;
      const cm = climByDoy[doy(r.obs_date)];
      if (cm && cm.gdd_f != null) nGdd += Number(cm.gdd_f);
      // Season rain vs normal: gridMET against the gridMET normal, and the normal
      // only on the days gridMET has a reading. A missing day is not 0".
      const gm = WX.gridmetPrecipMm(r);
      if (gm != null) {
        rain += gm / 25.4; lastGm = r.obs_date;
        if (cm && cm.precip_mm != null) nRain += Number(cm.precip_mm) / 25.4;
      }
    });
    const obs = wx.filter(r => !r.is_forecast);
    const last7 = obs.slice(-7).reduce((s, r) => s + (dayIn(r) || 0), 0);
    return {
      rain, gdd: Math.round(gdd), normRain: nRain, normGdd: Math.round(nGdd),
      rainPct: nRain > 0 ? Math.round(rain / nRain * 100) : null,
      gddPct: nGdd > 0 ? Math.round(gdd / nGdd * 100) : null,
      last7, next7: fcRain.slice(0, 7).reduce((s, v) => s + v, 0),
      next7High: fcHigh.length ? Math.round(fcHigh.slice(0, 7).reduce((s, v) => s + v, 0) / Math.min(7, fcHigh.length)) : null,
      daysSinceRain, lastObs, lastGm,
    };
  }, [wx, clim, outOfGrid, apiWx]);

  // ── LRP protection summary (dairy/cattle) — ACTIVE endorsements only: floor
  // value protected, head, weighted avg floor $/cwt, head by coverage-end month,
  // and the next expiring endorsement. insured_value is null book-wide, so floor
  // value comes from `liability`. ──
  const lrp = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const parseEnd = (d) => { if (!d) return null; const s = String(d); const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return new Date(+m[1], +m[2] - 1, +m[3]); const dt = new Date(s); return isNaN(dt) ? null : dt; };
    const seen = new Set(); const active = [];
    farmers.forEach(f => (f.lrpPolicies || []).forEach(p => (p.endorsements || []).forEach(e => {
      const k = `${e.lineId ?? ""}|${e.salesDate || e.sales_date || ""}|${e.endDate || e.end_date || ""}|${e.head ?? e.numberOfHead ?? ""}`;
      if (seen.has(k)) return; seen.add(k);
      const end = parseEnd(e.endDate || e.end_date); if (!end || end < today) return; // active only
      active.push({ head: Number(e.head ?? e.numberOfHead) || 0, floor: Number(e.insured_value ?? e.insuredValue ?? e.liability) || 0, price: Number(e.coveragePrice ?? e.coverage_price) || 0, end });
    })));
    if (!active.length) return null;
    const head = active.reduce((s, e) => s + e.head, 0);
    const floor = active.reduce((s, e) => s + e.floor, 0);
    const priced = active.filter(e => e.price > 0);
    const phead = priced.reduce((s, e) => s + e.head, 0);
    const avgPrice = phead ? priced.reduce((s, e) => s + e.price * e.head, 0) / phead : null;
    const byMonth = {};
    active.forEach(e => { const key = `${e.end.getFullYear()}-${String(e.end.getMonth() + 1).padStart(2, "0")}`; byMonth[key] = (byMonth[key] || 0) + e.head; });
    const maxM = Math.max(1, ...Object.values(byMonth));
    const months = Object.entries(byMonth).sort().map(([k, h]) => ({ k, h, frac: h / maxM, label: new Date(+k.slice(0, 4), +k.slice(5, 7) - 1, 1).toLocaleDateString("en-US", { month: "short" }) }));
    const sorted = [...active].sort((a, b) => a.end - b.end);
    const nextEnd = sorted[0].end;
    const nextHead = active.filter(e => e.end.getTime() === nextEnd.getTime()).reduce((s, e) => s + e.head, 0);
    return { head, floor, avgPrice, months, nextDate: nextEnd, nextHead };
  }, [farmers]);

  const card = { background: "#fff", border: "1px solid #dbe6da", borderRadius: 18, padding: isMobile ? 16 : 22,
                 boxShadow: "0 1px 2px rgba(18,36,26,0.04)" };
  const eyebrowS = { fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#60735f" };
  const h2S = { fontFamily: "'Lora',serif", fontSize: isMobile ? 19 : 22, fontWeight: 600, color: "#1a2e1a", margin: "4px 0 10px" };
  const money = (n) => n > 0 ? fmtD0(n) : "—";

  const nowD = new Date();
  const hr = nowD.getHours();
  const greet = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";
  const first = displayName ? String(displayName).split(/[\s,]/)[0] : "";
  const dateLong = nowD.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const nextIns = insEvents[0] || null;
  const nextInsLine = (() => {
    if (!nextIns) return "";
    const crop = String(nextIns.crop || "").toLowerCase();
    const when = `${fmtDate(nextIns.date)}${nextIns.daysAway === 0 ? " — today" : ` — in ${nextIns.daysAway} day${nextIns.daysAway === 1 ? "" : "s"}`}`;
    if (nextIns.kind === "acreage_reporting_date") return `Your ${nextIns.cropYear} ${crop} acreage report is due ${when}.`;
    if (nextIns.kind === "sales_closing_date") return `Sales closing for your ${nextIns.cropYear} ${crop} is ${when}.`;
    return `The final planting date for your ${nextIns.cropYear} ${crop} is ${when}.`;
  })();
  // Crops with protection on them, for the protection bar. A crop line with no
  // revenue (a forward crop year not yet reported) stays in the full table below,
  // where its blank says why, and stays off the bar, which only draws dollars.
  const protRows = roll.cropRows.filter((r) => r.rev > 0);
  const agenda = homeAgendaItems((mkt && mkt.calendar) || [], insEvents);

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto" }}>
      {/* ── MASTHEAD — date, greeting, the close in one sentence, the farm's weather ── */}
      <HomeMasthead isMobile={isMobile}
        greeting={`${greet}${first ? ", " + first : ""}.`}
        dateLong={dateLong}
        summary={boardSummaryLine(mkt)}
        extra={nextInsLine}
        weather={anyNY ? mastWeather : null} />

      {/* ── THE BOARD — price cards, each with its last 30 sessions ──
          Tiles are not navigable here: the portal's routes come from this
          customer's own grants, so a tile pointed at Grain or Quoters could be a
          dead click. */}
      <div style={{ position: "relative", marginTop: isMobile ? -30 : -38, padding: isMobile ? "0 8px" : "0 18px" }}>
        <HomeBoard data={mkt} isMobile={isMobile} parts="tiles" />
      </div>

      {/* ── YOUR ACCOUNT — only when this login reaches more than one operation ──
          A portal user's access to several farms is an access relationship, not
          permission to collapse them. So the account figure is stated ONLY with
          the words "across N operations" attached; an unlabelled 1,872 reads as
          one farm's acreage, which is the ambiguity this whole section exists to
          remove. The per-operation cards carry the numbers anyone would act on. */}
      {multiOp && (
        <div style={{ marginTop: 22 }}>
          <div style={{ ...card, background: "#12240f", marginBottom: 14 }}>
            <div style={{ ...eyebrowS, color: "#a9d38a" }}>Your account</div>
            <div style={{ fontFamily: "'Lora',serif", fontSize: isMobile ? 24 : 30, fontWeight: 700, marginTop: 5, color: "#fff" }}>
              {operations.length} operations
            </div>
            <div style={{ fontSize: 13.5, color: "#b7cdb3", marginTop: 4 }}>
              {acctAcres > 0 ? `${fmt(acctAcres, 0)} insured acres across all ${operations.length} operations` : "insured acreage not reported on these operations"}
              {acctCrops > 0 ? ` · ${acctCrops} crop${acctCrops === 1 ? "" : "s"}` : ""}
            </div>
          </div>
          <div style={{ ...eyebrowS, marginBottom: 8 }}>Your operations</div>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(300px,1fr))" }}>
            {opRolls.map(({ op, roll: r }) => (
              <div key={op.key} style={{ ...card, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, color: "#1a2e1a", fontFamily: "'Lora',serif", lineHeight: 1.25 }}>
                  {op.name}
                </div>
                {/* Each figure is this operation's OWN records. Missing acreage stays
                    missing - it is never zeroed and never borrowed from the sibling. */}
                <div style={{ fontSize: 13, color: "#4f6a4f" }}>
                  {r.totalAcres > 0 ? `${fmt(r.totalAcres, 0)} insured acres` : "insured acres not reported"}
                  {r.cropRows.length ? ` · ${r.cropRows.length} crop${r.cropRows.length === 1 ? "" : "s"}` : ""}
                  {r.counties ? ` · ${r.counties} ${r.counties === 1 ? "county" : "counties"}` : ""}
                </div>
                {r.cropRows.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 2 }}>
                    {r.cropRows.slice(0, 5).map((cr) => (
                      <div key={cr.crop} style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5 }}>
                        <span style={{ color: "#2d4a2d" }}>{cr.short || cr.crop}</span>
                        <span style={{ fontVariantNumeric: "tabular-nums", color: "#2d4a2d" }}>{cr.acres > 0 ? `${fmt(cr.acres, 0)} ac` : "—"}</span>
                      </div>
                    ))}
                    {r.cropRows.length > 5 && (
                      <div style={{ fontSize: 12, color: "#60735f" }}>+{r.cropRows.length - 5} more</div>
                    )}
                  </div>
                )}
                {onSelectOperation && (
                  <button onClick={() => onSelectOperation(op.key)}
                    style={{ marginTop: 6, alignSelf: "flex-start", padding: "10px 16px", borderRadius: 999, minHeight: 40,
                             border: "1px solid #cfe0cf", background: "#f4faf4", color: "#2d6a2d",
                             fontWeight: 700, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>
                    View operation →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 20, marginTop: 22, alignItems: "flex-start" }}>

        {/* ── LEFT: the news and the long view ── */}
        <div style={{ flex: "999 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 20 }}>
          {/* The same headlines the agent's Home shows — the first rows of the
              Markets news panels, headline / outlet / age / link and nothing else
              (2026-10-20). The Markets link is passed only when this customer's
              nav offers Markets. */}
          <HomeBriefing data={mkt} isMobile={isMobile} onMore={onOpenMarkets} />

          {/* MARKETS IN PERSPECTIVE — where THIS contract sat on THIS date in each
              of the last eleven years; contract-specific, never "whatever was front
              month". */}
          <HomePerspective data={mkt} isMobile={isMobile} months={months} setMonths={setMonths}
            perspKey={perspKey} setPerspKey={setPerspKey} />
        </div>

        {/* ── RIGHT: the customer's own ── */}
        <div style={{ flex: "1 1 340px", minWidth: 0, display: "flex", flexDirection: "column", gap: 20 }}>

          {/* YOUR PROTECTION — SINGLE-OPERATION ONLY. With two operations an
              unlabelled total reads as one farm's figure, which is precisely the
              confusion between account-level aggregation and farm-level data this
              page must never create; the operation cards above carry those. */}
          {!multiOp && roll.cropRows.length > 0 && (
            <section aria-label="Your protection" style={{ ...card, background: "#12241a", border: "none", color: "#f4f8f1" }}>
              <div style={{ ...eyebrowS, color: "#a9d38a" }}>Your protection</div>
              <div style={{ fontFamily: "'Lora',serif", fontSize: isMobile ? 36 : 44, fontWeight: 700, lineHeight: 1.05, marginTop: 8 }}>
                {money(roll.totalRev)}
              </div>
              <div style={{ fontSize: 14, color: "#c7d8c3", marginTop: 4 }}>
                revenue protected
                {roll.totalAcres > 0 ? ` · ${fmt(roll.totalAcres, 0)} insured acres` : ""}
                {roll.counties ? ` · ${roll.counties} ${roll.counties === 1 ? "county" : "counties"}` : ""}
              </div>
              {protRows.length > 0 && (
                <div style={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", marginTop: 16, gap: 2 }}>
                  {protRows.map((r) => (
                    <div key={r.crop} title={`${r.crop}: ${money(r.rev)}`}
                      style={{ flex: `${r.rev} 1 0`, background: r.color || CROP_COLOR[r.crop] || "#889" }} />
                  ))}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 14 }}>
                {protRows.map((r) => (
                  <div key={r.crop} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, flexShrink: 0, background: r.color || CROP_COLOR[r.crop] || "#889" }} />
                    <span style={{ flex: "1 1 auto", fontWeight: 600, textTransform: "capitalize" }}>{(r.short || r.crop).toLowerCase()}</span>
                    <span style={{ color: "#b4c7b2", fontVariantNumeric: "tabular-nums" }}>{r.acres > 0 ? `${fmt(r.acres, 0)} ac` : ""}</span>
                    <span style={{ fontWeight: 800, minWidth: 86, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(r.rev)}</span>
                  </div>
                ))}
              </div>
              {roll.supplementalRev > 0 && (
                <div style={{ fontSize: 12.5, color: "#b4c7b2", marginTop: 10 }}>
                  Includes {fmtD0(roll.supplementalRev)} of supplemental (SCO/ECO) protection.
                </div>
              )}
              {/* The full table — production guarantees and every crop line — one
                  tap away rather than leading the page. */}
              <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: "pointer", color: "#a9d38a", fontWeight: 700, fontSize: 14, minHeight: 32, display: "flex", alignItems: "center" }}>
                  Every crop, with its production guarantee
                </summary>
                <div style={{ marginTop: 10, background: "#fff", borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, color: "#1a2e1a" }}>
                      <thead>
                        <tr style={{ background: "#f4f8f4", color: "#4f6a4f", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 800 }}>Crop</th>
                          <th style={{ textAlign: "right", padding: "8px 8px", fontWeight: 800 }}>Acres</th>
                          <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 800 }}>Guarantee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roll.cropRows.map((r) => (
                          <tr key={r.crop} style={{ borderTop: "1px solid #eef2ee" }}>
                            <td style={{ padding: "9px 12px", fontWeight: 700 }}>
                              {r.crop}
                              {r.qty > 0 && <div style={{ fontSize: 11.5, fontWeight: 500, color: "#4f6a4f", marginTop: 2 }}>{fmt(r.qty, 0)} {r.unit} guaranteed</div>}
                              {r.note && <div style={{ fontSize: 11.5, fontWeight: 500, color: "#60735f", marginTop: 2 }}>{r.note}</div>}
                            </td>
                            <td style={{ padding: "9px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.acres > 0 ? fmt(r.acres, 0) : "—"}</td>
                            <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{money(r.rev)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {roll.cropRows.some((r) => !(r.rev > 0) && !(r.acres > 0)) && (
                    <div style={{ padding: "8px 12px 10px", fontSize: 11.5, color: "#60735f" }}>
                      — means the acres for that crop line have not been reported yet for this crop year.
                    </div>
                  )}
                </div>
              </details>
            </section>
          )}

          {/* DRP — PRF is a line in the protection card above */}
          {roll.drpCount > 0 && (
            <div style={card}>
              {multiOp && <div style={{ fontSize: 12, color: "#60735f", marginBottom: 6 }}>Across all {operations.length} operations</div>}
              <div style={eyebrowS}>Dairy Revenue Protection</div>
              <div style={{ fontFamily: "'Lora',serif", fontSize: 30, fontWeight: 700, color: "#1a2e1a", marginTop: 6 }}>{money(roll.drpLiab)}</div>
              <div style={{ fontSize: 13, color: "#60735f", marginTop: 2 }}>milk revenue protected · {roll.drpCount} DRP {roll.drpCount === 1 ? "endorsement" : "endorsements"}</div>
            </div>
          )}

          {/* LRP Protection — farmer-facing summary of ACTIVE endorsements */}
          {lrp && (
            <div style={card}>
              {multiOp && <div style={{ fontSize: 12, color: "#60735f", marginBottom: 6 }}>Across all {operations.length} operations</div>}
              <div style={eyebrowS}>Livestock Risk Protection</div>
              <h2 style={h2S}>Your LRP floors</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: lrp.months.length ? 14 : 0 }}>
                {[
                  ["Floor protected", money(lrp.floor), `${fmt(lrp.head, 0)} head`],
                  ["Avg floor", lrp.avgPrice != null ? `$${fmt(lrp.avgPrice, 0)}` : "—", "per cwt"],
                  ["Next ends", lrp.nextDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }), `${fmt(lrp.nextHead, 0)} head`],
                ].map(([label, val, sub]) => (
                  <div key={label} style={{ background: "#f4f8f4", borderRadius: 12, padding: "10px 10px", minWidth: 0 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "#60735f" }}>{label}</div>
                    <div style={{ fontSize: 19, fontWeight: 800, color: "#1a2e1a", marginTop: 3 }}>{val}</div>
                    <div style={{ fontSize: 11.5, color: "#60735f" }}>{sub}</div>
                  </div>
                ))}
              </div>
              {lrp.months.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#60735f", marginBottom: 8 }}>Head covered by coverage-end month</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 84 }}>
                    {lrp.months.map((m) => (
                      <div key={m.k} style={{ flex: "1 1 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }} title={`${m.label}: ${fmt(m.h, 0)} head`}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#2d6a2d", marginBottom: 3 }}>{fmt(m.h, 0)}</div>
                        <div style={{ width: "100%", maxWidth: 34, height: `${Math.max(6, m.frac * 56)}px`, background: "#7fb069", borderRadius: "4px 4px 0 0" }} />
                        <div style={{ fontSize: 11, color: "#60735f", marginTop: 4 }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* COMING UP — this grower's RMA dates (dark badge) beside the USDA
              reports that move their markets. */}
          <HomeAgenda items={agenda} isMobile={isMobile}
            footnote={insEvents.length ? "Dark dates are yours, from RMA's actuarial data for the counties and crops you insure." : null} />

          {/* SEASON SO FAR — nearest grid cell, vs the 30-yr normal */}
          {anyNY && (season ? (
            <HomeSeasonCard isMobile={isMobile} eyebrowText={mastWeather && mastWeather.label ? "Weather · " + mastWeather.label : "Weather at your farm"} title="Your season so far"
              rain={{ label: "Rainfall", value: `${season.rain.toFixed(1)}"`, now: season.rain, normal: season.normRain,
                      pct: season.rainPct, normalLabel: season.normRain ? `${season.normRain.toFixed(1)}" normal` : null }}
              gdd={{ label: "Heat (GDD)", value: fmt(season.gdd, 0), now: season.gdd, normal: season.normGdd,
                     pct: season.gddPct, normalLabel: season.normGdd ? `${fmt(season.normGdd, 0)} normal` : null }}
              extras={[["Last 7 days", `${season.last7.toFixed(2)}"`, "rain"],
                       ["Next 7 days", season.next7 != null ? `${season.next7.toFixed(2)}"` : "—", season.next7High != null ? `rain · ~${season.next7High}°F highs` : "forecast rain"]]}
              footnote={`${season.daysSinceRain != null && season.daysSinceRain > 3 ? `${season.daysSinceRain} days since ≥0.1″ of rain · ` : ""}GDD base 50°F (corn) · against the 1995–2025 normal${season.lastGm ? ` · rain through ${fmtDate(season.lastGm)}` : ""}${season.lastObs ? ` · observed through ${fmtDate(season.lastObs)}` : ""}.`} />
          ) : (
            <div style={card}><div style={eyebrowS}>Your season so far</div><div style={{ fontSize: 13, color: "#60735f", marginTop: 8 }}>Weather updating…</div></div>
          ))}

          {/* Local cash bids — per commodity, filtered to a radius of the farm. A
              posted bid is a fact about an elevator, not a recommendation of one. */}
          <div style={card}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={eyebrowS}>Local cash bids</div>
                <h2 style={{ ...h2S, marginBottom: 2 }}>Posted near you</h2>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {[[30, "30 mi"], [50, "50 mi"], [100, "100 mi"], [99999, "All"]].map(([mi, lab]) => (
                  <button key={mi} onClick={() => setRadiusMi(mi)} disabled={!origin && mi !== 99999}
                    style={{ border: "1px solid", borderColor: radiusMi === mi ? "#2d6a2d" : "#dbe6da", background: radiusMi === mi ? "#2d6a2d" : "#fff", color: radiusMi === mi ? "#fff" : (!origin && mi !== 99999 ? "#9aaa9a" : "#3d5f3d"), borderRadius: 999, padding: "6px 10px", minHeight: 32, fontSize: 12, fontWeight: 700, cursor: (!origin && mi !== 99999) ? "default" : "pointer", fontFamily: "inherit" }}>{lab}</button>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#60735f", margin: "4px 0 10px" }}>
              {origin ? <>Within {radiusMi === 99999 ? "any distance" : `${radiusMi} mi`} of {origin.src}</> : <>Add your farm address for nearby-only bids · showing all</>}
            </div>
            {(() => {
              // With a farm origin + a radius, a bid must have a KNOWN distance inside
              // that radius. Previously a bid whose elevator geo couldn't be resolved
              // (dist == null) was shown regardless — that's how WNY Energy leaked onto
              // a Michigan farm (Laier). "All" (99999) still shows everything; a farm we
              // can't place at all (no origin) falls back to showing all.
              const within = (b) => radiusMi === 99999 || (origin ? (b.dist != null && b.dist <= radiusMi) : true);
              const shown = [["CORN", "🌽"], ["WHEAT", "🌾"], ["SOYBEANS", "🫘"]]
                .map(([c, i]) => [c, i, (cashByCommodity[c] || []).filter(within)])
                .filter(([c]) => (cashByCommodity[c] || []).length);
              if (!shown.length) return <div style={{ fontSize: 13, color: "#60735f", padding: "10px 0" }}>Cash bids updating…</div>;
              let latest = "";
              shown.forEach(([, , a]) => a.forEach((b) => { if (b.date > latest) latest = b.date; }));
              return (
                <>
                  {shown.map(([c, icon, arr], ci) => (
                    <div key={c} style={{ paddingTop: ci ? 10 : 0, marginTop: ci ? 10 : 0, borderTop: ci ? "1px solid #eef2ee" : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                        <span style={{ fontSize: 15 }}>{icon}</span>
                        <span style={{ fontWeight: 800, color: "#1a2e1a", fontSize: 13.5, textTransform: "capitalize" }}>{c.toLowerCase()}</span>
                      </div>
                      {arr.length ? arr.slice(0, 5).map((b) => (
                        <div key={b.source} style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "4px 0 4px 22px", fontSize: 13.5 }}>
                          <span style={{ color: "#2d4a2d", fontWeight: 600, minWidth: 0 }}>{b.name}</span>
                          {b.dist != null && <span style={{ fontSize: 11.5, color: "#677167" }}>{Math.round(b.dist)} mi</span>}
                          <span style={{ fontSize: 11.5, color: "#677167" }}>{b.period}</span>
                          <span style={{ marginLeft: "auto", fontWeight: 800, fontSize: 15, color: "#12240f", fontVariantNumeric: "tabular-nums" }}>${b.price.toFixed(2)}</span>
                        </div>
                      )) : <div style={{ fontSize: 12, color: "#677167", padding: "2px 0 2px 22px" }}>none within {radiusMi} mi</div>}
                    </div>
                  ))}
                  <div style={{ fontSize: 11.5, color: "#a5461b", marginTop: 10, fontWeight: 700 }}>Delayed quotes — not live market prices · as of {latest ? fmtDate(latest) : "—"}</div>
                </>
              );
            })()}
          </div>

          <HomeBoard data={mkt} isMobile={isMobile} parts="side" />
        </div>
      </div>

      {/* ── WHAT INPUTS COST, IN BUSHELS ───────────────────────────────────
          A REGIONAL BENCHMARK, never a quote for this farm — the card prints the
          region and the report date on every row. `identity` scopes the per-acre
          input program's browser store to the EFFECTIVE user — the previewed
          farmer while preview is on, the real session otherwise. */}
      <HomePurchasingPower data={mkt} isMobile={isMobile} identity={getCurrentUserId()} />

      {/* A NAMED ABSENCE STAYS NAMED FOR A CUSTOMER TOO. What is filtered out
          server-side is the subset that names a FAILURE; a source we simply do
          not carry is still said out loud, because a missing card must not read
          as "nothing happened". */}
      {(mkt && mkt.absences && mkt.absences.length) ? (
        <div style={{ ...card, marginTop: 20 }}>
          <div style={{ ...eyebrowS, marginBottom: 6 }}>
            What these market pages don’t cover
          </div>
          {mkt.absences.map((a, i) => (
            <div key={i} style={{ fontSize: 12.5, color: "#60735f", lineHeight: 1.5 }}>
              <strong style={{ color: "#2d4a2d" }}>{a.source}</strong> — {a.reason}
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ fontSize: 12, color: "#60735f", margin: "22px 0 8px", textAlign: "center", lineHeight: 1.6 }}>
        Every number on this page is measured and opens to its source.
        <br />
        Questions about your coverage? Clover Ag · (585) 233-1066 · office@cloverag.com
      </div>
    </div>
  );
}

function Dashboard({ portalMode = null }) {
  const [farmers, setFarmers]           = useState([]);
  // Admin "view as" — when set (by the Portal Access screen), an admin renders the
  // portal exactly as that user sees it, scoped to their farms, with a preview
  // frame + exit. Stays null for real portal users, so their experience is unchanged.
  // Rehydrated from sessionStorage because entering preview hard-reloads the page
  // (so nothing the ADMIN already fetched survives into the farmer's session).
  // The token and this metadata are written together and cleared together.
  const [previewAs, setPreviewAs]       = useState(() => (isPreviewSession() ? getPreviewMeta() : null));
  // Demo/presentation farmers (metadata.demo) — kept separate so they never feed
  // any book/dashboard/by-AIP aggregate; injected only into the DRP list + detail.
  const [demoFarmers, setDemoFarmers]   = useState([]);
  // Book-wide customer list for File Management ONLY, and only for the EXPLICIT
  // book-wide 578 permission (portal_users.book_wide_578, sql/069) — the 578
  // capability alone no longer reaches it. Read from farmers_578_queue (id/name/county/state) so
  // the 578 queue never needs the full farmers row — phone is populated on 274
  // of 278 customers, address on 221, lat/lon on 207, and the queue renders a
  // name. Empty for everyone else. See sql/015 section 2.
  const [fileFarmerList, setFileFarmerList] = useState([]);
  const [pricesByYear, setPricesByYear]     = useState(DEFAULT_PRICES_BY_YEAR);
  const [dryBeanPrices, setDryBeanPrices]   = useState(DEFAULT_DRY_BEAN_PRICES);
  // The RMA feed lands in a module-level store (effectivePrice is a plain
  // function, not a hook), so this counter is what tells React the prices on
  // screen just changed. Bumped once, after hydration.
  const [rmaFeedNonce, setRmaFeedNonce]     = useState(0);
  // A nav key persisted before the Book merge (e.g. "spring") redirects to its
  // replacement rather than dropping the user on a screen that's no longer in
  // the sidebar. The old routes still resolve — this is about where you LAND.
  const [nav, setNav] = useState(() => {
    const saved = sessionStorage.getItem("cloverag_nav") || "home";
    return NAV_LEGACY_ROUTES[saved] || saved;
  });
  // Apply the legacy-route redirect at nav TIME too, not just on initial load — otherwise
  // any runtime setNavPersist("spring"/"fall"/…) lands on a retired page the sidebar no longer
  // lists (the "Back dropped me on the spring page" bug). Retired agent routes → their
  // replacement.
  const setNavPersist = (page) => { const p = NAV_LEGACY_ROUTES[page] || page; sessionStorage.setItem("cloverag_nav", p); setNav(p); };
  // Quick switcher: Ctrl+K / Cmd+K anywhere, or "/" when no text field has focus.
  // Agent app only — the listener is attached here but the switcher is only
  // rendered in the agent shell, so the portal never opens it.
  const [switcherOpen, setSwitcherOpen] = useState(false);
  useEffect(() => {
    // The portal (and admin preview of it) renders through this same component
    // but never shows the switcher — don't swallow its "/" keystrokes for nothing.
    if (portalMode || previewAs) return undefined;
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(tag) || (e.target && e.target.isContentEditable);
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); setSwitcherOpen(o => !o); }
      else if (e.key === "/" && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); setSwitcherOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [portalMode, previewAs]);
  // Which LOB's market a coverage→market jump should focus in the Markets hub.
  // A stored retired LRP key (see NAV_LEGACY_FOCUS) seeds it on load.
  const [marketFocus, setMarketFocus] = useState(() => {
    try { return navLegacyFocus(sessionStorage.getItem("cloverag_nav"), "markets"); } catch { return null; }
  });
  // Which Quoters tab a stored retired DRP/LRP key should open on.
  const [quoterFocus] = useState(() => {
    try { return navLegacyFocus(sessionStorage.getItem("cloverag_nav"), "quoters"); } catch { return null; }
  });
  // Sidebar section open/collapsed state (persisted). Data & Admin starts collapsed.
  // Keyed by NAV_MODEL group title. Groups default OPEN (absent === open), so the
  // toggle has to read the same "!== false" rule the renderer does — using !p[k]
  // on an absent key would evaluate to true and the first click wouldn't close.
  // v3 key: the v2 values were the old coverage/tools/admin group names.
  // Settings is ten admin pages nobody opens daily, so it starts COLLAPSED;
  // every other group starts open. A stored choice always wins.
  const navGroupIsOpen = (p, k) => (k in p) ? p[k] !== false : !NAV_GROUPS_DEFAULT_CLOSED.has(k);
  const [navOpen, setNavOpen] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("cloverag_navgroups_v3") || "null") || {}; } catch { return {}; }
  });
  const toggleNavGroup = (k) => setNavOpen(p => {
    const n = { ...p, [k]: !navGroupIsOpen(p, k) };
    sessionStorage.setItem("cloverag_navgroups_v3", JSON.stringify(n));
    return n;
  });
  const [selectedId, setSelectedId]     = useState(null);
  // Every customer card opened — from the list, the switcher or anywhere else —
  // goes to the front of the quick switcher's "Recent" list. Memory only.
  useEffect(() => {
    if (!selectedId) return;
    const i = _recentCustomerIds.indexOf(selectedId);
    if (i >= 0) _recentCustomerIds.splice(i, 1);
    _recentCustomerIds.unshift(selectedId);
    _recentCustomerIds.length = Math.min(_recentCustomerIds.length, 6);
  }, [selectedId]);
  // WHICH OPERATION THE USER IS LOOKING AT. Persisted so it survives a reload,
  // and deliberately NOT an authorization input - see the narrowing guard where
  // portalFarmers is scoped. A stale key for a farm the user no longer holds
  // simply matches nothing and the full authorized set is used.
  const [selectedOpKey, setSelectedOpKeyRaw] = useState(() => {
    try { return sessionStorage.getItem("cloverag_portal_op") || null; } catch { return null; }
  });
  const setSelectedOpKey = (k) => {
    try { if (k) sessionStorage.setItem("cloverag_portal_op", k); else sessionStorage.removeItem("cloverag_portal_op"); } catch {}
    setSelectedOpKeyRaw(k || null);
  };
  const [bookSection, setBookSection]   = useState(null);   // detail section chosen from a Book row
  const [fieldsFarmerId, setFieldsFarmerId] = useState(null);   // Field Mapper deep-link target
  const openFields = (fid) => { setFieldsFarmerId(fid || null); setSelectedId(null); setNavPersist("fields"); };
  // ⚠ ONE FIELD, NOT ONE FARM — the Crop Review "farm / tract / field" cell links straight to the
  // boundary on the map. Same one-shot shape as `fieldsFarmerId` above and `grainDeepLink` below:
  // the destination consumes it on arrival and clears it, so a later visit does not re-jump.
  // The CLU is passed as the AIP spells it; FieldMapPanel's focus effect folds the case itself,
  // which it must — policy_fields is 59% UPPER and field_geometries is all lower.
  const [fieldFocus, setFieldFocus] = useState(null);   // { farmerId, cluId } | null
  const openFieldOnMap = (farmerId, cluId) => {
    if (!cluId) return openFields(farmerId);            // no boundary to aim at — open the farm
    setFieldsFarmerId(farmerId || null);
    setFieldFocus({ farmerId: farmerId || null, cluId });
    setSelectedId(null);
    setNavPersist("fields");
  };
  // Grain Book View row -> Grain Summary deep-link target (ROADMAP.md §1.2f
  // Milestone 4's "click through" requirement). Same shape as fieldsFarmerId
  // above — a one-shot target GrainMarketingView consumes once on mount.
  const [grainDeepLink, setGrainDeepLink] = useState(null);   // { farmerId, crop, cropYear } | null
  const openGrainFarm = (farmerId, crop, cropYear) => { setGrainDeepLink({ farmerId, crop, cropYear }); setSelectedId(null); setNavPersist("grain"); };
  const [loading, setLoading]           = useState(true);
  const [dbError, setDbError]           = useState(null);
  const isMobile                        = useIsMobile();

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        // ⚠ THE PREMIUM-FREE `*_578` VIEWS NEVER EXISTED IN PRODUCTION (measured
        // 2026-09-11). sql/010 defines crop_coverage_578 and four siblings; no
        // production migration ever created them. This loader used to swap them in
        // for any 578 flag holder, so it asked for five relations that are not
        // there, each request failed, and the `.catch(() => [])` beside it turned
        // the failure into an EMPTY book: a limited agent holding the flag saw no
        // coverage, DRP, LRP or PRF for her OWN customers, silently.
        //
        // Every caller reads the base tables now and RLS decides. A limited agent
        // sees her own grants; an explicitly book-wide holder (sql/069) sees what
        // her policies allow. `bookViewer` survives only to fetch the 578 queue,
        // and only for the EXPLICIT book-wide permission, never for the flag alone.
        const bookViewer = !isCurrentUserAdmin() && !!(portalMode && portalMode.bookWide578);

        // The narrowed customer list for the 578 queue. Fetched only for a 578
        // holder, and tolerant of the view not existing yet: if sql/015 hasn't
        // run, this comes back empty and the queue falls back to `farmers`,
        // which still works today because viewer_farmers_read_all is still in
        // place. That makes the app and the migration safe to land in either
        // order — the wide policy can be dropped once both are live.
        if (bookViewer) {
          sbFetch("farmers_578_queue", { select: "id,name,county,state" })
            .then(rows => setFileFarmerList(Array.isArray(rows) ? rows : []))
            .catch(() => setFileFarmerList([]));
        }

        // ⚠ THE FARMERS READ KEEPS ITS OWN FAILURE. `.catch(() => [])` on the
        // others is a tolerable trade; on this one it is the difference between
        // an empty agency and a request that was refused.
        let farmersReadError = null;
        const [farmerRows, policies, cropCoverage, ppEligibility,
               drpEndorsements, lrpEndorsements, priceRows,
               prfPolicyRows, prfIntervalRows, indemnityRows, prod2025Rows, premiumPaymentRows] = await Promise.all([
          sbFetch("farmers").catch((e) => { farmersReadError = e; return []; }),
          sbFetch("policies").catch(() => []),
          sbFetch("crop_coverage").catch(() => []),
          sbFetch("pp_eligibility").catch(() => []),
          sbFetch("drp_endorsements").catch(() => []),
          sbFetch("lrp_endorsements").catch(() => []),
          sbFetch("price_settings").catch(() => []),
          sbFetch("prf_policies").catch(() => []),
          sbFetch("prf_intervals").catch(() => []),
          sbFetch("indemnities").catch(() => []),
          // LAST YEAR'S PRODUCTION, for the "missing production" filter. This read runs
          // beside the policies, before the book's year is known, so it takes the
          // years around the calendar's and the consumer below keeps CURRENT_CROP_YEAR − 1
          // once it is adopted. Was crop_year=eq.2025 — frozen (rollover, Kevin 2026-11-02).
          sbFetch("production_history", { select: "policy_id,farmer_id,crop,county,acres,yield_per_acre,record_type,crop_year", filters: [`crop_year=in.(${new Date().getFullYear() - 2},${new Date().getFullYear() - 1},${new Date().getFullYear()})`,"source_file=eq.M13_auto"] }).catch(() => []),
          // THE GROWER'S OWN PREMIUM PAYMENTS — M13 record P12. Stored in a table
          // called `loss_notices` with the payment type in `cause_code`, which is a
          // misnaming CLAUDE.md has recorded since 2026-08-19: P12 is Payment, not
          // Notice of Loss. Read here so the farmer card can say what is still owed.
          // `crop_year` on these rows is the payment-DATE year (the file year is
          // `file_crop_year`) and is deliberately NOT selected — payments join on
          // policy_id, and filtering on that column drops $1.88M of real payments
          // against CY2026 policies.
          sbFetch("loss_notices", { select: "policy_id,cause_code,amount,loss_event_date,flag,file_crop_year" }).catch(() => []),
        ]);

        // NOTE: Legacy cleanup blocks removed (Apr 2026). M13 is now the source of truth
        // for crop_coverage acres. Any cleanup of stale PDF/SOI data happens in the sync pipeline.

        console.log(`loadData: ${farmerRows.length} farmers, ${policies.length} policies, ${cropCoverage.length} cov, ${indemnityRows.length} indemnities`);

        // policy id → crop_year, so product-specific views can use their own
        // policy's year instead of the farmer-wide max.
        // Carrier per policy row. PRF/LRP must be bucketed on the POLICY's AIP,
        // never the farmer's — 8 of the PRF book's farms hold both NAU and R&H
        // paper, and the merged farmer object collapses to aip:"Multiple", which
        // put their PRF in a bucket of its own on Book of Business by AIP.
        // Roll the live crop year off the book itself, before anything derived
        // from it is built. No annual code edit.
        adoptCropYearFromBook(policies, cropCoverage);
        // ...and record what has already rolled PAST it, so the app can say so
        // out loud instead of leaving it to be inferred. Must run after the
        // adopt above — it reads CURRENT_CROP_YEAR to know which year is ahead.
        summarizeForwardYear(policies, cropCoverage);

        const policyAipById = {};
        const policyCropYearById = {};
        // State of the POLICY, not the farmer. `farmers.state` is NULL book-wide on
        // most rows; `policies.state` is what the AIP file sets, and it is what the
        // PRF grid map opens on so a Michigan farm does not land on a New York map.
        const policyStateById = {};
        for (const p of policies) if (p?.id != null) { policyCropYearById[p.id] = p.crop_year; policyAipById[p.id] = p.aip || null; policyStateById[p.id] = p.state || null; }

        // ── Hydrate USDA_MONTHLY_PRICES from the class_prices table (AMS LMPR sync) ──
        // This eliminates the need to manually update the hardcoded object each month.
        // Hardcoded values remain as fallback if the table query fails.
        try {
          const cpRows = await sbFetch("class_prices", { select: "year,month,milk_class,price" });
          if (cpRows && cpRows.length > 0) {
            let patchCountIII = 0, patchCountIV = 0;
            cpRows.forEach(r => {
              const key = `${r.year}-${String(r.month).padStart(2, "0")}`;
              const p = parseFloat(r.price);
              if (!isNaN(p)) {
                if (r.milk_class === "III") { USDA_MONTHLY_PRICES.III[key] = p; patchCountIII++; }
                else if (r.milk_class === "IV") { USDA_MONTHLY_PRICES.IV[key] = p; patchCountIV++; }
              }
            });
            console.log(`class_prices hydration: ${patchCountIII} Class III + ${patchCountIV} Class IV monthly prices loaded into USDA_MONTHLY_PRICES`);
          }
        } catch (e) {
          console.warn("class_prices hydration failed, using hardcoded USDA_MONTHLY_PRICES:", e?.message || e);
        }

        // ── Hydrate the RMA price feed from the ADM (api/market?fn=rma-price) ──
        // This is what makes an announced price appear on its own. The ladder in
        // resolveRmaPrice() prefers a published RMA value, falls back to the
        // verified constant, then to the live futures tracker — and refuses a
        // feed price that disagrees wildly with the constant, so a failed pull or
        // a wrong commodity code degrades to the number we already trusted rather
        // than moving a guarantee. Its own try/catch: a class_prices failure must
        // not take the crop prices down with it.
        try {
          const rmaRows = await sbFetch("rma_prices", {
            // state_code is selected, not just filtered, so buildRmaFeed's own state
            // guard can actually see it rather than trusting this filter to stay put.
            select: "crop_year,commodity_code,state_code,type_code,practice_code,plan_code,projected_price,harvest_price,volatility,released_date",
            filters: [`state_code=eq.${RMA_FEED_STATE}`, `crop_year=gte.${CURRENT_CROP_YEAR - 1}`],
          });
          if (rmaRows && rmaRows.length) {
            setRmaPriceFeed(rmaRows);
            setRmaFeedNonce(n => n + 1);
            console.log(`rma_prices hydration: ${rmaRows.length} ADM rows loaded`);
          }
        } catch (e) {
          console.warn("rma_prices hydration failed — prices fall back to the verified table:", e?.message || e);
        }

        // ── Hydrate the SETTLED projected-price discovery average ──────────
        // Runs AFTER the ADM hydration on purpose: a published RMA price wins on
        // rung 1, so a pair that the ADM already answers is never fetched at all.
        // We fetch only pairs that are (a) PENDING after rungs 1 and 2 and (b)
        // past their published discovery window — i.e. the number is determined
        // and RMA simply has not posted it yet. Today that is exactly CY2027
        // wheat; in February it will be exactly CY2027 corn and soybeans, with no
        // edit here. A pair whose window is still open is deliberately skipped:
        // a running average is the market-screen's job, not the book's.
        try {
          const today = new Date().toISOString().slice(0, 10);
          const want = [];
          for (const crop of Object.keys(PRICE_DISCOVERY)) {
            for (const yr of [CURRENT_CROP_YEAR, CURRENT_CROP_YEAR + 1]) {
              if (resolveRmaPrice(crop, yr, "projected").status !== "pending") continue;
              const win = discoveryWindow(crop, yr, "projected");
              if (!win || !win.to || today <= win.to) continue;   // still open — not settled
              want.push({ crop, yr, win });
            }
          }
          for (const w of want) {
            const bars = await sbFetch("futures_contract_history", {
              filters: [`commodity=eq.${w.win.commodity}`, `contract_sym=eq.${w.win.sym}`],
              select: "bar_date,close", order: "bar_date.asc",
            });
            if (bars && bars.length) {
              setDiscoveryBars(w.crop, w.yr, bars);
              const r = resolveRmaPrice(w.crop, w.yr, "projected");
              console.log(`discovery hydration: ${w.crop} CY${w.yr} ${w.win.sym} -> ${r.price != null ? "$" + r.price.toFixed(4) : "none"} (${r.status}, ${r.days || 0} days)`);
            }
          }
          if (want.length) setRmaFeedNonce(n => n + 1);
        } catch (e) {
          console.warn("discovery hydration failed — CY prices stay pending rather than guessing:", e?.message || e);
        }

        if (priceRows.length > 0) {
          const built = {};
          const dryBeans = { ...DEFAULT_DRY_BEAN_PRICES };
          priceRows.forEach(row => {
            // Dry bean type prices are stored as crop='DRYBEAN_BTS' etc.
            if (row.crop?.startsWith('DRYBEAN_')) {
              const typeKey = row.crop.replace('DRYBEAN_', '');
              dryBeans[typeKey] = Number(row.price_guarantee);
            } else {
              if (!built[row.crop_year]) built[row.crop_year] = {};
              const _dbPrice  = Number(row.price_guarantee);
              const _rmaPrice = RMA_SPRING_PRICES[row.crop_year]?.[row.crop];
              // Use official RMA projected price if DB value is within $1.00 of it (stale entry).
              // Values differing by more than $1.00 are harvest prices — keep those.
              if (_rmaPrice != null && Math.abs(_dbPrice - _rmaPrice) <= 1.00) {
                built[row.crop_year][row.crop] = _rmaPrice;
              } else {
                built[row.crop_year][row.crop] = _dbPrice;
              }
            }
          });
          // Merge per-year so organic keys from DEFAULT_PRICES_BY_YEAR are never wiped by Supabase entries
          const merged = { ...DEFAULT_PRICES_BY_YEAR };
          Object.entries(built).forEach(([yr, crops]) => {
            merged[yr] = { ...(DEFAULT_PRICES_BY_YEAR[yr] || {}), ...crops };
          });
          setPricesByYear(merged);
          setDryBeanPrices(dryBeans);
        }

        // ── PER-TYPE price elections from the RMA ADM (rma_prices) ──────────────
        // Some crops have no futures discovery, so RMA publishes a fixed election
        // per TYPE. Keyed <CROP>_<type_code> because the price differs by type but
        // NOT by county (verified across all 13 NY grape counties), so one row per
        // type is enough. Nothing here is hardcoded — if the sync hasn't run, the
        // price stays unknown and the UI shows "—" instead of a guess.
        //
        // Onions joined grapes on 2026-08-12. They were the last unpriceable rows
        // in the book: RMA prices onions per type, our two coverages carried no
        // type, so $565,379 of liability rendered a guarantee of "—". Their SOIs
        // state the types (Orleans is Reds at $25.80 AND Yellows at $15.60 — which
        // is also why it could never have been priced as one blended row).
        try {
          const PER_TYPE_PRICED = [
            { commodity: "0053", key: "GRAPES" },
            { commodity: "0013", key: "ONIONS" },
          ];
          const gRows = await sbFetch("rma_prices", {
            select: "crop_year,commodity_code,type_code,projected_price",
            filters: [`commodity_code=in.(${PER_TYPE_PRICED.map(c => c.commodity).join(",")})`,
                      "plan_code=eq.90", "projected_price=not.is.null"] });
          if (gRows && gRows.length) {
            const keyFor = Object.fromEntries(PER_TYPE_PRICED.map(c => [c.commodity, c.key]));
            const byYear = {};
            gRows.forEach(g => {
              const p = Number(g.projected_price);
              const k = keyFor[g.commodity_code];
              if (!isFinite(p) || !g.type_code || !k) return;
              (byYear[g.crop_year] || (byYear[g.crop_year] = {}))[`${k}_${g.type_code}`] = p;
            });
            setPricesByYear(prev => {
              const next = { ...prev };
              Object.entries(byYear).forEach(([yr, m]) => { next[yr] = { ...(next[yr] || {}), ...m }; });
              return next;
            });
            console.log(`rma_prices: per-type elections loaded for ${Object.keys(byYear).length} crop year(s)`);
          }
        } catch (e) {
          console.warn("grape price hydration failed — price column will show '—':", e?.message || e);
        }

        // ⚠ AN EMPTY BOOK READ IS NAMED, NEVER SUBSTITUTED. This used to assign
        // SEED_FARMERS — four REAL customers with hardcoded addresses, phone
        // numbers and stale CY2025 figures — and `return`, so the page rendered a
        // fabricated book with no banner and no empty state. See
        // bookReadFailureMessage for why the row count alone cannot say why.
        if (farmerRows.length === 0) {
          setFarmers([]);
          // ⚠ SCOPED BOTH DIRECTIONS, and the portal half cannot be re-derived
          // from this code — it rests on a measurement. A FAILED read is never a
          // legitimate state, so it errors for everybody. A SUCCESSFUL read of
          // zero rows IS legitimate in the portal: two live accounts hold no farm
          // grants at all — jnevinger (market pages only) and
          // mmeisenzahl@cloverag.com (alerts only) — and the dbError screen
          // returns ABOVE the portal branch, so erroring there would blank the
          // market pages those two accounts exist for. The agency always has
          // growers, so in the agent app zero rows is always a fault.
          //
          // `isPreviewSession()` rather than the `previewAs` STATE: this effect
          // has empty deps and must keep them, and entering or leaving preview
          // hard-reloads the page by design (see beginPreviewSession), so the
          // sessionStorage token cannot change within a mount. Reading the state
          // here would be an undeclared dependency on a value that can never move.
          if (farmersReadError || !(portalMode || isPreviewSession())) {
            setDbError(bookReadFailureMessage(farmersReadError));
          }
          return;
        }

        // ── Build one farmer object per farmer_id, merging ALL policy rows ──
        // A farmer can have multiple policy rows (different crop years, different types).
        // We want ONE card per farmer with ALL their data combined.
        // DRP especially: 2025 SOI + 2026 SOI = 1 farmer, all endorsements together.

        // Helper to map a DRP endorsement row from Supabase → JS object
        // Derive DRP crop year from effective date: July+ → next CY, Jan-Jun → same CY
        // DRP CY2026 = endorsements sold July 1 2025 – June 30 2026
        // deriveDrpCropYear is defined at top level

        const mapDrpEnd = (e, policyCropYear) => {
          // CROP YEAR COMES FROM THE AIP, NOT FROM A DERIVATION.
          // `soi_crop_year` is the year the AIP itself filed the endorsement under
          // — the sync writes it straight from P18 field 1 (buildDrpRows). It is
          // populated on every sync-loaded row; only 74 legacy manual/PDF rows
          // from CY2025 lack it, which is what the fallbacks are for.
          //
          // The July-1 derivation currently agrees with the AIP on all 143
          // endorsements in the 2025 and 2026 files, so this changes no number
          // today. It is here because agreement is a coincidence we don't
          // control: R&H files an LRP endorsement effective 07/01/2025 under
          // CY2025, while NAU files a DRP endorsement effective 07/01/2024 under
          // CY2025 — same calendar boundary, opposite side. Two carriers, two
          // conventions, and no rule we can write covers both. Take their value.
          const derivedCY = e.soi_crop_year ?? policyCropYear ?? deriveDrpCropYear(e.effective_date);
          return {
          endNum: e.end_num, quarter: e.quarter, type: e.type, practiceType: e.practice_type,
          effectiveDate: e.effective_date, endDate: e.end_date, insShr: e.ins_shr,
          declaredMilkProd: e.declared_milk_prod, expMilkPerCow: e.exp_milk_per_cow,
          // class_iii: 1=Class III, 0=Class IV. If null, derive from type column (M13 imports)
          classIII: e.class_iii != null ? e.class_iii
            : (e.type === "III" ? 1 : e.type === "IV" ? 0 : null),
          // Class III share in percent (M13 P18 #29 x 100). 1-99 is a blend, which
          // the estimate prices as a blend (drpClassIIIWeight); null on Class IV rows.
          classIIIPct: e.class_iii_pct ?? null,
          coverageLevel: e.coverage_level,
          // Derive projectedPrice if not stored: triggerPrice = projectedPrice × coverageLevel
          projectedPrice: e.projected_price != null ? e.projected_price
            : (e.trigger_price != null && e.coverage_level > 0)
              ? Math.round((e.trigger_price / e.coverage_level) * 100) / 100
              : null,
          triggerPrice: drpExactProtectedPrice(e), expRevGuar: e.exp_rev_guar, liability: e.liability,
          totalPremium: e.total_premium, subsidy: e.subsidy, producerPremium: e.producer_premium,
          cropYear: derivedCY || policyCropYear || null,
        };};

        // Group all policies by normalized farmer name.
        // A farmer can have multiple rows in the farmers table (e.g. uploaded as two
        // separate SOIs for different crop years → two farmer_id values). Grouping by
        // normalized name ensures they always merge into one card regardless of DB state.
        // ── Indexes for the per-policy joins below ────────────────────────────
        // These loops used to be `.filter()` calls INSIDE the per-policy loop, so
        // every one of ~700 policies re-scanned all ~4,700 child rows — about 3.3M
        // comparisons per page load, growing with the square of the book. Building
        // the indexes once up front makes the same work linear. Purely a
        // restructuring of HOW the rows are found; which rows each policy gets is
        // unchanged.
        const byPolicy = (rows, key = "policy_id") => {
          const m = new Map();
          for (const r of rows || []) {
            const k = r?.[key];
            if (k == null) continue;
            const bucket = m.get(k);
            if (bucket) bucket.push(r); else m.set(k, [r]);
          }
          return m;
        };
        // NOTE: each bucket belongs to exactly one policy.id, so no two consumers
        // share an array — same ownership `.filter()` gave. The miss case returns a
        // FRESH `[]` (never a shared singleton) so a later push can't leak across
        // policies.
        const farmerById       = new Map((farmerRows || []).map(r => [r.id, r]));
        const coverageByPolicy = byPolicy(cropCoverage);
        const ppByPolicy       = byPolicy(ppEligibility);
        const drpByPolicy      = byPolicy(drpEndorsements);
        const lrpByPolicy      = byPolicy(lrpEndorsements);
        const indemByPolicy    = byPolicy(indemnityRows);
        const payByPolicy      = byPolicy(premiumPaymentRows);

        // ── WHAT A POLICY IS, WHEN THE FILE HAS ALREADY ROLLED ────────────────
        // Both AIPs roll their file mid-summer, so from July the book carries
        // NEXT year's policy rows. Part of that roll is real and wanted: CY2027
        // winter wheat is being sold right now (344 fall-crop elections across
        // 121 farms) and livestock rolls on July 1 by rule. The rest has not
        // rolled — CY2027 holds 0 spring-crop rows and 0 PRF rows.
        //
        // The forward-year file carries only the MPCI side of a policy, because
        // PRF, DRP and LRP for that year are not sellable yet. So the sync
        // faithfully types those rows `crop` — 36 PRF + 6 LRP + 5 DRP policies
        // as of 2026-08-13, every one of them carrying ZERO coverage rows. That
        // is the FILE being early, not the policy changing product: MP-0928663
        // is Marquart Dairy's PRF policy in both years.
        //
        // So a policy's line of business is read from the newest year that has
        // actually ARRIVED, and a forward-year row never restates it. Without
        // this a PRF-only farm grew a "CROP" badge and its card header named a
        // `-27` policy number over CY2026 numbers — the same CY2027-on-a-PRF-card
        // bug the crop-year clamp below was written for, reappearing through the
        // one field the clamp didn't cover.
        const isForwardYear = (p) => (p && p.crop_year ? p.crop_year : 0) > CURRENT_CROP_YEAR;
        const typeByStem = new Map();
        [...policies]
          .filter(p => p && p.policy_type && !isForwardYear(p) && paPolicyStem(p.policy_number))
          .sort((a, b) => (a.crop_year || 0) - (b.crop_year || 0))
          .forEach(p => typeByStem.set(paPolicyStem(p.policy_number), p.policy_type));

        const farmerMap = {};
        policies.forEach(policy => {
          const f = farmerById.get(policy.farmer_id);
          if (!f) return;
          // Each policy is its own card — group by name + policyNumber.
          // Different crop years of the same policy collapse to one card.
          // Spring crop, wheat, DRP, LRP, PRF are naturally separate because they have different policy numbers.
          // NAU embeds the crop year in the number (NY-944-1011047-26 vs -27), so that
          // collapse silently failed for them — every NAU farmer got a second card per
          // crop year, and the newest one is empty until acreage is reported. R&H numbers
          // (MP-0930443) carry no year and already collapsed. Strip the year suffix so
          // both AIPs behave the same. Only matches the NAU shape, so R&H, ProAg, Sompo
          // and hand-entered numbers are untouched.
          const fid = `${f.name.trim().toLowerCase()}||${normalizePolicyCardKey(policy.policy_number)}`;

          const coverage = coverageByPolicy.get(policy.id) || [];

          const pp       = ppByPolicy.get(policy.id)  || [];
          const drp      = drpByPolicy.get(policy.id) || [];
          const lrp      = lrpByPolicy.get(policy.id) || [];

          // Derive policyType: trust stored policy_type (set by M13 sync) as source of truth.
          // M13 sync determines type from plan codes and patches it on every run.
          // Only fall back to data-based derivation for policies not yet touched by M13.
          // A forward-year row defers to what the policy already is; it only
          // speaks for itself when the policy is new and has no arrived year.
          const storedType = (isForwardYear(policy) && typeByStem.get(paPolicyStem(policy.policy_number)))
                          || policy.policy_type || '';
          const validM13Types = ['crop','prf','lrp','drp'];
          let policyType;
          if (validM13Types.includes(storedType)) {
            // M13 has set this — trust it
            policyType = storedType;
          } else {
            // Legacy/manual policy — derive from child data
            const hasCropCoverage = coverage.length > 0 || pp.length > 0;
            const hasPRF = prfPolicyRows.some(p => p.policy_id === policy.id);
            const hasLRP = lrp.length > 0;
            const hasDRP = drp.length > 0;
            policyType = hasCropCoverage ? 'crop'
                       : hasLRP ? 'lrp'
                       : hasDRP ? 'drp'
                       : hasPRF ? 'prf'
                       : 'crop';
          }
          const cropYr = policy.crop_year || CURRENT_CROP_YEAR;

          if (!farmerMap[fid]) {
            // First policy for this farmer — initialise
            farmerMap[fid] = {
              id:           f.id,              // actual DB farmer row id (first seen)
              _primaryPolicyId: policy.id,     // for save operations
              _allFarmerIds: [f.id],           // all DB farmer row ids sharing this name
              name:         f.name,
              entityType:   f.entity_type   || "",
              agentCode:    f.agent_code    || "",
              policyNumber: policy.policy_number || "",
              cropYear:     CURRENT_CROP_YEAR,   // resolved from non-livestock policies at cleanup
              _cropDisplayYear: 0,               // max crop_year among NON-livestock policies
              county:       policy.county   || "",
              state:        policy.state    || "NY",
              agent:        policy.agent_name  || "",
              agency:       policy.agency_name || "",
              insurer:      policy.insurer  || "",
              // Provisional — resolved from _aipSet at cleanup. A farmer holding
              // paper from two carriers has no single aip, and taking the first
              // policy's silently credited a whole farm to one arbitrary carrier.
              aip:          policy.aip      || "NAU",
              _aipSet:      new Set(),
              policyTypes:  [],
              metadata:     f.metadata || null,   // carries demo flag, drpEstProd, etc.
              address:      f.address || "",
              phone:        f.phone   || "",
              // farmers.lat/lon (2026-08-18 §A) — the SAME columns
              // PortalHomeView's cash-bid radius already reads off this table.
              // Carried onto the card so the Grain cash engine's geography
              // eligibility (src/positions/grainCash.js) has a real farm
              // location when one is on file; null (not 0/0) otherwise, so
              // "unknown" stays distinguishable from "at the equator".
              lat:          f.lat != null ? f.lat : null,
              lon:          f.lon != null ? f.lon : null,
              approvedYields:      [],
              scoEcoEndorsements:  [],
              ppEligibility:       [],
              lrpPolicies:         [],
              drpPolicies:    [],   // flat list of all endorsements across all crop years
              prfPolicies:    [],
              claims:         [],   // renamed from claims — holds P20/P20A indemnity data
              // P12 premium payments, one row per payment, carrying the policy they
              // were made on. Never keyed on crop year — see the fetch above.
              premiumPayments: [],
              _drpByPolicy:   {},   // policy_number -> {aip, ends[]}
              _allLrpEnds:    [],
              _allCropYears:  [],
              _allPolicyNums: [],
              _allPolicyIds:  [policy.id],  // all DB policy row ids — for PRF lookup
            };
          }

          const farmer = farmerMap[fid];
          if (policy.aip) farmer._aipSet.add(policy.aip);

          // Manual Agent-of-Record override (any policy carrying one wins, even a
          // livestock-only book that never reaches the crop-year agent assignment
          // below). Preferred over the sync-owned agent_name everywhere it's shown.
          if (policy.agent_name_override && !farmer._agentOverride) farmer._agentOverride = policy.agent_name_override;

          // Track all DB farmer ids (for reset operations that must cover all rows)
          if (!farmer._allFarmerIds.includes(f.id)) farmer._allFarmerIds.push(f.id);

          // Track all crop years and policy numbers for display
          if (cropYr && !farmer._allCropYears.includes(cropYr)) farmer._allCropYears.push(cropYr);
          if (policy.policy_number && !farmer._allPolicyNums.includes(policy.policy_number))
            farmer._allPolicyNums.push(policy.policy_number);

          // Track all policy IDs for PRF lookup
          if (!farmer._allPolicyIds) farmer._allPolicyIds = [];
          if (!farmer._allPolicyIds.includes(policy.id)) farmer._allPolicyIds.push(policy.id);

          // Display crop year = most recent NON-LIVESTOCK policy year. Livestock
          // (DRP/LRP) rolls to the next reinsurance year on July 1, and letting
          // that set farmer.cropYear dragged every crop/PRF price + PP lookup
          // (all keyed on farmer.cropYear) a year ahead of the actual crop book —
          // that's the CY2027-on-a-PRF-card bug. Livestock views derive their own
          // year from endorsement effective dates and never read this field.
          //
          // A year that has ARRIVED beats a forward year outright, and only then
          // does the newest win. A bare `cropYr >` comparison made the winner
          // depend on Supabase row order: whichever of MP-0928663's two rows the
          // loader happened to see first set the header, so the same farm named
          // its CY2027 policy on one load and its CY2026 policy on the next. The
          // rank makes it deterministic, and a farm whose ONLY paper is forward
          // (8 of them, mostly next year's wheat) still gets its number rather
          // than a blank.
          const identityRank = cardYearRank(cropYr);
          if (!isLivestockPolicyType(policyType) && identityRank > (farmer._identityRank || 0)) {
            farmer._identityRank    = identityRank;
            farmer._cropDisplayYear = Math.min(cropYr, CURRENT_CROP_YEAR);
            farmer.policyNumber = policy.policy_number || farmer.policyNumber;
            farmer.agent        = farmer._agentOverride || policy.agent_name || farmer.agent;
            farmer.agency       = policy.agency_name   || farmer.agency;
            farmer.county       = policy.county        || farmer.county;
          }

          // Accumulate policy types — start with the stored/derived primary type
          if (policyType && !farmer.policyTypes.includes(policyType))
            farmer.policyTypes.push(policyType);
          // LRP+DRP may share a policy number — ensure both types are tracked
          if (lrp.length > 0 && !farmer.policyTypes.includes('lrp'))
            farmer.policyTypes.push('lrp');
          if (drp.length > 0 && !farmer.policyTypes.includes('drp'))
            farmer.policyTypes.push('drp');

          // Separate SCO/ECO rows from base coverage rows — they are stored together in crop_coverage
          // but must be handled separately: base rows get averaged by unit, SCO/ECO rows go to scoEcoEndorsements
          // RPHPE (Revenue Protection with Harvest Price Exclusion) covers the same acres as RP — exclude from base to prevent double-counting
          // ARPI is the NAU/M13 plan code for Supplemental Coverage Option (rendered as SCO-RP everywhere downstream).
          // Without it here, an ARPI row is treated as base coverage and collapses into the same county|crop|type|
          // practice group as the real RP base row (group key has no plan), summing their premium/liability and
          // making the merged row inherit plan='ARPI' — which FallView then hides from base rows. (Meisenzahl wheat bug.)
          const SCO_ECO_PLANS = ['SCO-RP', 'ECO-RP', 'SCO', 'ECO', 'SCO-YP', 'ECO-YP', 'ARPI', 'RPHPE'];
          const baseCoverage = coverage.filter(c => !SCO_ECO_PLANS.includes((c.plan||"").toUpperCase()));
          const scoCoverage  = coverage.filter(c =>  SCO_ECO_PLANS.includes((c.plan||"").toUpperCase()));

          // Load SCO/ECO into scoEcoEndorsements — keyed by county+crop+type+practice+plan
          // Multiple units per key are summed (premium, liability, acres) not overwritten
          scoCoverage.forEach(c => {
            // CROP YEAR IS PART OF THE KEY. Without it two crop years of the same
            // ECO row collided and this block ADDED them together, so the card
            // showed 2026 + 2027 stacked. It reads $0 of damage today only
            // because the forward-year shells carry null premium — it would bite
            // the moment next season is keyed. Same defect class as PRF/LRP.
            const key = `${cropYr}|${(c.county||"").toUpperCase()}|${(c.crop||"").toUpperCase()}|${(c.type||"").toUpperCase()}|${normalizePractice(c.practice).toUpperCase()}|${(c.plan||"").toUpperCase()}`;
            const existing = farmer.scoEcoEndorsements.find(e =>
              `${e.cropYear}|${(e.county||"").toUpperCase()}|${(e.crop||"").toUpperCase()}|${(e.type||"").toUpperCase()}|${normalizePractice(e.practice).toUpperCase()}|${(e.plan||"").toUpperCase()}` === key
            );
            if (existing) {
              existing.totalPremium = (existing.totalPremium||0) + (Number(c.total_premium)||0);
              existing.netPremium   = (existing.netPremium||0)   + (Number(c.net_premium)||0);
              existing.liability    = (existing.liability||0)    + (Number(c.liability)||0);
              existing.acres        = (existing.acres||0)        + (Number(c.acres)||0);
            } else {
              farmer.scoEcoEndorsements.push({
                cropYear: cropYr,
                // Carrier of the policy that wrote this endorsement — same reason
                // approvedYields carries one. The farmer's own `aip` is whichever
                // policy the loader saw first and is wrong for anyone holding
                // paper from two carriers.
                aip: policyAipById[policy.id] || null,
                // WHICH POLICY WROTE THIS OVERLAY — the same reason approvedYields
                // carries it. The AIP bills per policy and charges an ECO/SCO line
                // its own $30, so an overlay with no policy on it cannot be billed
                // at all: Anchor's two ECO-RP lines went missing from his bill for
                // exactly this reason. Additive; the dedupe key above is unchanged.
                policyId: policy.id || null,
                policyNumber: policy.policy_number || null,
                county: c.county, crop: c.crop, type: c.type,
                practice: normalizePractice(c.practice), plan: c.plan,
                coverageLevel: c.coverage_level, acres: Number(c.acres)||0,
                totalPremium: Number(c.total_premium)||0,
                netPremium:   Number(c.net_premium)||0,
                liability:    Number(c.liability)||0,
              });
            }
          });

          // Merge approvedYields — group by county+crop+type+practice, average yields across units
          // Supabase stores one row per unit (since extraction now returns individual units).
          // We average here so the app always shows one row per county+crop+type+practice.
          // Identity falls back to type_code when the human-readable type is absent.
          // M13 leaves `type` null for specialty crops, which silently merged rows
          // that are genuinely different coverages — NY grapes carry a per-variety
          // RMA price ($413/ton type 200 vs $614/ton type 562), so blending them
          // produced one meaningless APH. Only reaches rows where type IS null:
          // 13 groups book-wide (grapes 11, apples 1, cabbage 1), no grain impact.
          const covIdent = (c) => ((c.type || "").trim() || (c.type_code || "")).toUpperCase();
          const coverageGroups = {};
          baseCoverage.forEach(c => {
            const key = `${(c.county||"").toUpperCase()}|${(c.crop||"").toUpperCase()}|${covIdent(c)}|${normalizePractice(c.practice).toUpperCase()}`;
            if (!coverageGroups[key]) coverageGroups[key] = [];
            coverageGroups[key].push(c);
          });
          Object.entries(coverageGroups).forEach(([key, rows]) => {
            const validYields = rows.map(r => r.aph_yield).filter(y => y != null && y > 0);
            // Keep one decimal. Rounding to a whole number is invisible on a 146 bu
            // corn APH but destroys a specialty yield — grapes run 6–9 tons/acre, so
            // 8.7 became 9 and carried a 3.4% error into the guarantee.
            const avgYield = validYields.length > 0
              ? Math.round((validYields.reduce((s,y) => s+Number(y), 0) / validYields.length) * 10) / 10
              : null;
            const first = rows[0];
            const row = {
              county: first.county, crop: first.crop, plan: first.plan,
              practice: normalizePractice(first.practice), type: first.type,
              // RMA numeric type — the price key for crops priced per variety (grapes).
              typeCode: first.type_code || null,
              // Needed to match this coverage to its units (see cuKey). Without it an
              // organic/irrigated split would find the wrong practice's units.
              practiceCode: first.practice_code || null,
              // Carrier of the policy that wrote this coverage. Needed by any
              // per-AIP rollup — the farmer's own `aip` collapses to "Multiple"
              // for anyone holding paper from two carriers.
              aip: policyAipById[policy.id] || null,
              // WHICH POLICY WROTE THIS COVERAGE. The AIP bills per policy and
              // applies a claim credit against the POLICY's balance, so nothing
              // can reconcile to a statement without knowing this. Purely
              // additive metadata — the dedupe key below is unchanged.
              policyId: policy.id || null,
              policyNumber: policy.policy_number || null,
              // ── THE PREVENT-PLANT BUY-UP ELECTION (BUG-010) ────────────────────
              // insurance_options is P14 f[37], the exact string the SOI prints in
              // its "Options, Election, or Endorsements" column. `PF` in it means
              // the grower bought the prevent-plant factor UP, and until 2026-09-04
              // every PP surface priced them at the base factor instead.
              //
              // `optionsCaptured` is the THIRD state and is why this is two fields
              // rather than one: a blank string on a SYNC-FORMAT policy is the AIP
              // saying nothing was elected, and a blank one on a hand-typed Agri
              // Sompo / ProAg book is us never having asked. Collapsing them makes
              // 52 of the book's 779 CY2026 eligibility rows claim a base factor
              // nobody stated. Same rule cbsOptState() holds for the Book Scan.
              insuranceOptions: (rows.find(r => r.insurance_options) || first).insurance_options || null,
              optionsCaptured: isSyncFormatPolicyNumber(policy.policy_number),
              // The ADM prevent-plant table is keyed (state, county, practice).
              state: policy.state || null,
              approvedYield: avgYield,
              coverageLevel: (rows.find(r => r.coverage_level != null) || first).coverage_level,
              unitCount: rows.reduce((s,r) => s + (Number(r.unit_count) || 1), 0),
              priceElection: first.price_election,
              // Price-election fraction (1.0=100% buy-up, 0.55=CAT, 0.5/0.75/0.8=reduced ECO).
              // Drives the CAT/reduced-election haircut on PP + liability. NULL → treated as 1.0.
              priceElectionPct: (rows.find(r => r.price_election_pct != null) || first).price_election_pct ?? null,
              coverageType: (rows.find(r => r.coverage_type != null) || first).coverage_type ?? null,
              // EU / BU / OU as the AIP wrote it — additive, for the prior-year line the
              // Fall section prints ("2026: 75% RP · EU"). Nothing computes with it.
              unitStructure: (rows.find(r => r.unit_structure) || first).unit_structure || null,
              cropYear: cropYr,
              // Sum acres across all units in group (not just first row)
              acres: rows.reduce((s,r) => s + (Number(r.acres)||0), 0) || null,
              // Premium fields — sum across units in group (not average)
              totalPremium: rows.reduce((s,r) => s + (Number(r.total_premium)||0), 0) || null,
              subsidy:      rows.reduce((s,r) => s + (Number(r.subsidy)||0), 0) || null,
              netPremium:   rows.reduce((s,r) => s + (Number(r.net_premium)||0), 0) || null,
              liability:    rows.reduce((s,r) => s + (Number(r.liability)||0), 0) || null,
              // PREVENT PLANT, summed the same way as acres beside it. `acres` above is
              // INSURED acres and insured = planted + PP, so anything reading this row
              // without ppAcres cannot tell the two apart — which is exactly how the
              // grain tool came to count prevent-plant ground as ground that grows
              // bushels. NULL stays NULL: not determined is not the same as none.
              ppAcres: rows.some(r => r.pp_acres != null)
                ? rows.reduce((s,r) => s + (Number(r.pp_acres)||0), 0) : null,
            };
            const existing = farmer.approvedYields.find(a =>
              `${(a.county||"").toUpperCase()}|${(a.crop||"").toUpperCase()}|${((a.type||"").trim() || (a.typeCode||"")).toUpperCase()}|${normalizePractice(a.practice).toUpperCase()}` === key
            );
            // The dedupe key has no crop year, so two years of the same policy collide
            // here (R&H reuses one policy_number across years). A blind overwrite made
            // the winner depend on Supabase row order — and next crop year's wheat is
            // written months before acreage is reported, so it arrives with null acres
            // and null liability. Landing second, it blanked cards that had real 2026
            // coverage (J Rice, Waltons Way). Prefer the row that actually carries
            // coverage; break ties on the newer crop year. Deterministic either way.
            const hasCoverage = a => (a.acres != null || a.liability != null) ? 1 : 0;
            if (!existing) farmer.approvedYields.push(row);
            else if (hasCoverage(row) > hasCoverage(existing)) Object.assign(existing, row);
            else if (hasCoverage(row) === hasCoverage(existing) && (row.cropYear||0) >= (existing.cropYear||0)) Object.assign(existing, row);

            // ⚠ THE SAME ROW, KEPT PER CROP YEAR — because the merge above COLLAPSES years.
            // Its key has no crop year, so one CY2026 and one CY2027 coverage for the same
            // county become ONE row, and when neither carries acreage the newer year wins.
            // The Fall revenue table then priced that CY2027 row with the card's CY2026 price
            // map: Thomas Nagle, Blair wheat, CY2027 APH 70 bu x the CY2026 $5.76 = $302.40/ac
            // printed as a guarantee (measured 2026-09-16, live on production). A screen that
            // shows one crop year reads THIS map instead, so it can never be handed a row from
            // a year it did not ask for. Additive: every reader of approvedYields is unchanged.
            // Copies, never shared objects — the merge above mutates its rows in place.
            {
              const _ayKey = (a) => `${(a.county||"").toUpperCase()}|${(a.crop||"").toUpperCase()}|${((a.type||"").trim() || (a.typeCode||"")).toUpperCase()}|${normalizePractice(a.practice).toUpperCase()}`;
              const _byY = (farmer.approvedYieldsByYear = farmer.approvedYieldsByYear || {});
              const _yRows = (_byY[cropYr] = _byY[cropYr] || []);
              const _yEx = _yRows.find(a => _ayKey(a) === key);
              if (!_yEx) _yRows.push({ ...row });
              else if (hasCoverage(row) >= hasCoverage(_yEx)) Object.assign(_yEx, { ...row });
            }
          });

          // Merge ppEligibility — key on county+crop+type+practice so organic and conventional stay separate
          pp.forEach(e => {
            const key = `${(e.county||"").toUpperCase()}|${(e.crop||"").toUpperCase()}|${(e.type||"").toUpperCase()}|${normalizePractice(e.practice).toUpperCase()}`;
            const existing = farmer.ppEligibility.find(p =>
              `${(p.county||"").toUpperCase()}|${(p.crop||"").toUpperCase()}|${(p.type||"").toUpperCase()}|${normalizePractice(p.practice).toUpperCase()}` === key
            );
            const row = {
              county: e.county, crop: e.crop, practice: normalizePractice(e.practice), type: e.type,
              yr1: e.yr1, yr1Year: e.yr1_year, yr2: e.yr2, yr2Year: e.yr2_year,
              yr3: e.yr3, yr3Year: e.yr3_year, yr4: e.yr4, yr4Year: e.yr4_year,
              maxEligAcres: e.max_acres, maxEligYear: e.max_acres_year,
              plantedAcres:       e.planted_acres != null ? String(e.planted_acres) : "",
              aphYield:           e.aph_yield          ?? null,
              coverageLevel:      e.pp_coverage_level  ?? null,
              ppLiabilityPerAcre: e.pp_liability_per_acre ?? null,
            };
            // ⚠ THIS KEY HAS NO CROP YEAR IN IT, AND THE CARD COLLAPSES YEARS.
            // A blind Object.assign let the LAST policy the loader happened to
            // visit decide a grower's prevent-plant eligibility: measured
            // 2026-09-15, 207 card lines carry two crop years and 54 DISAGREE,
            // 3,293 acres apart.
            //
            // ⚠ AND THE NEWEST YEAR WINS HERE, NOT THE ARRIVED ONE — see
            // ppYearRank. R L Jeffres' LIVINGSTON wheat is the case: CY2026 says
            // 369.50 and CY2027 says 925.60, because CY2027's four-year window
            // includes the 925.6 acres he actually planted for CY2026. The
            // CY2027 figure is the one his prevented acres would be measured
            // against this autumn, and it is the only one that exists for every
            // fall farm.
            row.cropYear = cropYr || null;
            row._yearRank = ppYearRank(cropYr);
            if (!existing) farmer.ppEligibility.push(row);
            else if (row._yearRank > (existing._yearRank || 0)) Object.assign(existing, row);
            // Kept per crop year as well, for the same reason as approvedYieldsByYear: the
            // merge above keeps the NEWEST year, so a CY2026 view could otherwise only ever
            // be handed CY2027 eligibility. First policy wins within a year, as above.
            {
              const _ppByY = (farmer.ppEligibilityByYear = farmer.ppEligibilityByYear || {});
              const _pRows = (_ppByY[cropYr] = _ppByY[cropYr] || []);
              if (!_pRows.some(p => `${(p.county||"").toUpperCase()}|${(p.crop||"").toUpperCase()}|${(p.type||"").toUpperCase()}|${normalizePractice(p.practice).toUpperCase()}` === key)) _pRows.push({ ...row });
            }
          });

          // Accumulate DRP endorsements per policy number
          if (drp.length) {
            const pn = policy.policy_number || 'UNKNOWN';
            if (!farmer._drpByPolicy[pn]) farmer._drpByPolicy[pn] = { aip: policy.aip||'NAU', county: policy.county||'', ends: [] };
            drp.forEach(e => {
              const key = drpEndKey(e);
              if (!farmer._drpByPolicy[pn].ends.find(x => drpEndKey(x) === key)) {
                // the policy's own state picks the endorsement's RMA milk yield ID (src/drpYieldAdjust.js)
                farmer._drpByPolicy[pn].ends.push({ ...mapDrpEnd(e, cropYr), policyState: policy.state || null });
              }
            });
          }

          // Accumulate LRP endorsements — deduplicate by natural key (salesDate+endDate+head+coveragePrice)
          // lineId is just a sequence number and is not stable across multiple policy rows
          lrp.forEach(e => {
            const mapped = {
              lineId: e.line_id, commodity: e.commodity, type: e.type, county: e.county,
              salesDate: e.sales_date, endDate: e.end_date,
              weeksOut: e.weeks_out, head: e.head, targetWeight: e.target_weight,
              coveragePrice: e.coverage_price, insuredValue: e.insured_value, premium: e.total_premium ?? e.premium ?? null,
              producerPremium: e.producer_premium, subsidy: e.subsidy, billingDate: e.billing_date,
              actualEndValue: e.actual_end_value, payableIndemnity: e.payable_indemnity ?? null,
              liability: e.liability ?? null,
              _policyNumber: policy.policy_number || null,
              _aip: policy.aip || null,
              // Crop year comes from the POLICY ROW, not a sales-date roll.
              // The AIP already applies the reinsurance-year roll when it assigns
              // an endorsement to a policy year — the policy row IS the
              // reinsurance year — so deriving it again from the sales date
              // double-applies it and disagrees with the carrier's own statement.
              // Checked against Rain & Hail's CY2026 LRP Policy Detail (06 Aug
              // 2026): by policy year we match them exactly, 9 policies / 132
              // endorsements / $274,832, agency by agency. Rolling on the sales
              // date instead dragged two 07/01/2025 endorsements out of CY2025
              // and read $278,132 / 134. That statement is what gets reconciled
              // against, so the policy row wins.
              cropYear: cropYr || null,
            };
            // The key must identify an ENDORSEMENT, not just a purchase date.
            // salesDate+endDate+coveragePrice was too loose: Edwin Moran bought
            // Heifers Weight 1 (6 head @ 5.00 cwt, $568) and Steers Weight 2
            // (20 head @ 7.50 cwt, $2,838) on the same day, same end date, same
            // $342.79 coverage price — so one was discarded as a duplicate and
            // the LRP total read $568 under the Rain & Hail statement. Head and
            // target weight are what separate them; a genuine cross-policy
            // duplicate still matches on every field.
            const lrpKey = (e) => [e.salesDate, e.endDate, e.coveragePrice,
                                   e.head, e.targetWeight, e.type].join('|');
            const dupeIdx = farmer._allLrpEnds.findIndex(x => lrpKey(x) === lrpKey(mapped));
            if (dupeIdx >= 0) {
              // Merge: fill in nulls from the newer row (handles data from different policy rows for same farmer)
              const old = farmer._allLrpEnds[dupeIdx];
              Object.keys(mapped).forEach(k => {
                if ((old[k] == null || old[k] === 0) && mapped[k] != null && mapped[k] !== 0) old[k] = mapped[k];
              });
            } else {
              farmer._allLrpEnds.push(mapped);
            }
          });

          // Accumulate indemnities (P20/P20A data) for this policy
          // Premium the grower has PAID against this policy (M13 P12). Attached
          // per policy rather than per crop year: a fall crop is paid the previous
          // autumn, so the payment's own file year says nothing about which
          // policy it settles. ⚠ THE JOIN ALONE DOES NOT SCOPE THE YEAR — this
          // comment used to claim it did. 235 of 334 payments are attached to a
          // policy row for a crop year they did not pay for, so `fileCropYear`
          // is carried and the shared rule refuses a proven mismatch.
          (payByPolicy.get(policy.id) || []).forEach(pmt => {
            farmer.premiumPayments.push({
              policyId: policy.id,
              policyNumber: policy.policy_number || null,
              policyType: policy.policy_type || null,
              cropYear: policy.crop_year ?? null,
              amount: Number(pmt.amount) || 0,
              paymentType: pmt.cause_code || null,
              paymentDate: pmt.loss_event_date || null,
              fileCropYear: pmt.file_crop_year ?? null,
              flag: pmt.flag || null,
            });
          });

          const policyIndemnities = indemByPolicy.get(policy.id) || [];
          // Premium credits (loss_code M/F) settle WITH the claim's paper check.
          // Map each loss_key to its paper-check (E/L) date so credits inherit
          // the real settlement date instead of their ledger date (which, on a
          // claim that also carries an ECO, is the 6/12 ECO-reconciliation date).
          const settleDateByLossKey = {};
          policyIndemnities.forEach(c => {
            if ((c.loss_code === "E" || c.loss_code === "L") && c.check_date && c.loss_key) {
              if (!settleDateByLossKey[c.loss_key] || c.check_date < settleDateByLossKey[c.loss_key])
                settleDateByLossKey[c.loss_key] = c.check_date;
            }
          });
          policyIndemnities.forEach(c => {
            const pType = c.policy_type || policyType || storedType || 'crop';
            const TYPE_LABELS = { lrp:"LRP Indemnity", drp:"DRP Indemnity", prf:"PRF Indemnity", crop:"Crop Indemnity" };
            // Payment method comes from CLAIM_PAY_METHOD — the ONE map every claim
            // surface reads. The local table this replaced called E "Escrow Funded"
            // while the Claims page called the same code a check, and had no entry
            // at all for M/F, so a premium credit rendered as a bare "— M".
            const payMethod = claimPayMethod(c.loss_code);

            // loss codes M/F = PREMIUM CREDIT — real indemnity applied against
            // premium owed (confirmed vs R&H claim register: Danielewicz corn PP
            // $42,628 = $10,615 check (E) + $32,013 premium credit (M+F)). They
            // settle WITH the claim, so they're Paid — but dated to the claim's
            // paper-check date, NOT their ledger date (which on an ECO-bearing
            // loss_key is the 6/12 reconciliation date → the McIlroy "$3,632 PP
            // paid 6/12" bug; the real PP settled 7/31/2025). Tagged paymentType
            // "Premium Credit" so the UI shows it's a credit, not a cash check.
            const isPremiumCredit = c.loss_code === "M" || c.loss_code === "F";
            // loss_code D = ECO/SCO area-plan escrow. UNFUNDED: when R&H funds
            // the ECO the D vanishes and reappears as a dated E paper check
            // (verified 2026-06-16 — 45 of 47 CY2025 D escrows became E checks
            // dated 6/15). So a funded ECO is already Paid via its E check (and
            // labeled ECO/SCO through its FL stage); a dateless D is still OWED,
            // so it must NOT be forced to Paid — it keeps its Pending status and
            // shows on Active as a pending ECO. The wording comes from
            // CLAIM_PAY_METHOD ("ECO/SCO escrow") so it matches every other screen.
            // LRP/DRP indemnities arrive as V (Verified) / Q (Adjustment) with no
            // separate E check — for these endorsement products V/Q IS the settled
            // payout (confirmed Kevin 2026-06-15). Paid, not stuck on Active.
            const isEndorsementSettled = (pType === "lrp" || pType === "drp") && (c.loss_code === "V" || c.loss_code === "Q");
            // O/P/R are small adjustment components that ride on a claim's loss_key
            // alongside its E/L paper check. Verified 2026-06-16: every R&H O/P/R
            // sits on a loss_key with a paid E check (or on a credit-only settled
            // claim). They're part of a settled claim → Paid, dated to the
            // loss_key's paper-check date when one exists (else dateless).
            const isSatellite = c.loss_code === "O" || c.loss_code === "P" || c.loss_code === "R";
            const isSettled = isPremiumCredit || isEndorsementSettled || isSatellite;
            // A prior-crop-year claim still flagged Open/In Adjustment with $0 and
            // no disbursement is RESOLVED — a notice of loss that closed with no
            // indemnity (e.g. a wheat HAR that came in above guarantee), not current
            // work. Mark Closed so it drops off the Active tab. Verified 2026-06-16:
            // all 17 such NAU rows carry an adjuster + 2025 date but $0 on the policy.
            const claimAmt = parseFloat(c.indemnity_amount) || 0;
            const isStaleOpen = (c.status === "Open" || c.status === "In Adjustment")
              && claimAmt === 0 && (c.crop_year || 0) < CURRENT_CROP_YEAR;   // the BOOK's year (adopted above), not the calendar's: Jan 1 would close every open $0 claim
            const claimStatus = isSettled ? "Paid" : (isStaleOpen ? "Closed" : (c.status || "Pending"));
            const claimCheckDate = (isPremiumCredit || isSatellite)
              ? (settleDateByLossKey[c.loss_key] || c.check_date)
              : c.check_date;
            // A D row IS a payment record in its own right — money the AIP has
            // confirmed and not yet funded — so it keeps an explicit paymentType
            // rather than being filed away as generic claim money.
            //
            // BUT IT IS NOT "ECO/SCO". This line hardcoded that label until
            // 2026-08-19, when Kevin found a prevent-plant corn claim on a base
            // MPCI policy (Gillette Ag, MP-0938967, Cayuga, $7,879) captioned
            // ECO/SCO ESCROW. Measured over every D row in the book: 9 of 9 are
            // prevent-plant, 4 of 9 sit on a cell with no SCO or ECO coverage at
            // all, and ZERO carry the P21 FL area-plan line that would make them
            // ECO. renderStageCell already had this right — it calls a D
            // "ECO/SCO" only when claimHasEco() confirms an FL line — and this
            // site simply never got the same guard. The label now comes from
            // CLAIM_PAY_METHOD, which says what is verified: escrow, awaiting
            // funding. What coverage it belongs to decodes from RMA ICE D00070,
            // which we do not hold.
            const paymentType = payMethod.label;

            farmer.claims.push({
              policyId: policy.id,
              policyNumber: policy.policy_number,
              policyType: pType,
              claimNumber: c.claim_number,
              lossCode: c.loss_code,
              lossCodeName: payMethod.label,
              // Crop name + crop_year on the claim row itself — these come from
              // the indemnity record (M13 P14 → cid → crop, and the canonical
              // crop_year field set by the sync). Required so the Tracker can
              // surface e.g. "WHEAT · CY25" — without these, a 2025 wheat ECO/
              // SCO payment paid out under a 2026 policy row (the documented
              // -26 → -25 fallback) is indistinguishable from any other crop
              // indemnity and Kevin can't find it.
              crop: c.crop || null,
              cropYear: c.crop_year || null,
              county: c.county || null,
              // Claim-stage codes (NAU P20X PP/HAR/REP, R&H P21 PF/H/R) and the
              // adjuster name. Carried through so the Claims page → Active tab
              // can show what stage an open claim is in and who's working it —
              // the early-warning data (Sullivan's open soybean REP, etc.).
              claimStages: c.claim_stages || null,
              adjuster: c.adjuster || null,
              lossKey: c.loss_key || null,
              description: pType === "crop" && c.crop ? `${c.crop}${c.crop_year ? ` · CY${String(c.crop_year).slice(2)}` : ""}` : (TYPE_LABELS[pType] || "Indemnity"),
              indemnityAmount: parseFloat(c.indemnity_amount) || 0,
              // M13 P20 f[8] — the AIP's own stated total for this claim. Carried
              // so the Claims page can prove its disbursements sum to it rather
              // than asking anyone to take the arithmetic on faith. NAU only;
              // R&H P20 has no total field, so it stays null there.
              claimTotal: c.claim_total != null ? Number(c.claim_total) : null,
              paymentType,
              checkDate: claimCheckDate,
              claimDate: c.claim_date || null,
              noticeDate: c.notice_date || null,   // P58 field 25 — lets the Date cell say "Noticed"
              // Activity date for sorting: check_date > claim_date > created_at.
              // Without this, Verified/Pending rows with no check_date sort to
              // the bottom (epoch 1970) and look invisible in the date-desc view.
              activityDate: claimCheckDate || c.claim_date || c.created_at || null,
              status: claimStatus,
              aip: c.aip || policy.aip || "NAU",
            });
          });
        });

        // Build 2025 production data per farmer+crop from M13 P15A records
        // Used for "Missing 2025 Production" filter
        // Match by farmer_id (not policy_id) so AIP transfers don't create false positives
        // e.g. farmer transferred NAU→R&H: 2025 production is under old NAU policy_id
        //      but farmer_id stays the same across both policies
        const prod2025ByFarmerCrop = {}; // farmer_id → { "COUNTY|CROP" → { hasAcres, hasYield, county, crop } }
        // Build policy_id → farmer_id lookup from all policies
        const policyToFarmer = {};
        policies.forEach(p => { if (p.id && p.farmer_id) policyToFarmer[p.id] = p.farmer_id; });
        const priorProdYear = CURRENT_CROP_YEAR - 1;   // adopted from the book above
        (prod2025Rows||[]).forEach(r => {
          if (Number(r.crop_year) !== priorProdYear) return;
          // Use farmer_id directly if available, otherwise derive from policy_id
          const fid = r.farmer_id || policyToFarmer[r.policy_id];
          if (!fid) return;
          if (!prod2025ByFarmerCrop[fid]) prod2025ByFarmerCrop[fid] = {};
          const cropUp = (r.crop||"").toUpperCase();
          const countyUp = (r.county||"").toUpperCase().trim();
          const key = `${countyUp}|${cropUp}`;
          if (!prod2025ByFarmerCrop[fid][key]) {
            prod2025ByFarmerCrop[fid][key] = { hasAcres: false, hasYield: false, county: countyUp, crop: cropUp };
          }
          const entry = prod2025ByFarmerCrop[fid][key];
          if (Number(r.acres) > 0) entry.hasAcres = true;
          if (Number(r.yield_per_acre) > 0) entry.hasYield = true;
        });
        // Attach to farmer objects — missing = had 2025 acres but no 2025 yield for any spring crop
        Object.values(farmerMap).forEach(farmer => {
          const springYields = (farmer.approvedYields||[]).filter(a => !FALL_CROPS.includes((a.crop||"").toUpperCase()));
          if (springYields.length === 0) { farmer._has2025Prod = true; return; }
          // Check all farmer DB IDs (some farmers have multiple DB rows from merges)
          const farmerIds = farmer._allFarmerIds || [farmer.id];
          const farmerCropStatus = {};
          farmerIds.forEach(fid => {
            const entries = prod2025ByFarmerCrop[fid] || {};
            Object.entries(entries).forEach(([key, status]) => {
              if (!farmerCropStatus[key]) farmerCropStatus[key] = { ...status };
              else {
                if (status.hasAcres) farmerCropStatus[key].hasAcres = true;
                if (status.hasYield) farmerCropStatus[key].hasYield = true;
              }
            });
          });
          // Missing = any county+crop where they had 2025 acres but no 2025 yield
          const missingEntries = Object.values(farmerCropStatus)
            .filter(s => s.hasAcres && !s.hasYield && !FALL_CROPS.includes(s.crop));
          farmer._has2025Prod = missingEntries.length === 0;
          farmer._missing2025Crops = missingEntries.map(s => {
            const county = s.county ? s.county.charAt(0) + s.county.slice(1).toLowerCase() : "";
            const crop = s.crop ? s.crop.charAt(0) + s.crop.slice(1).toLowerCase() : "";
            return county ? `${crop} (${county})` : crop;
          });
        });

        // (Removed: surfacing raw P12 Notices of Loss as Active "Open" claims.
        // They proved confusing and low-signal — early notices with no adjuster,
        // often cause-code 00, sometimes snapshot-duplicated, and on NAU they're
        // redundant with the P20X open claims that DO carry adjuster/stage/crop.
        // Active claims now come only from real loss records (P20X open + paid),
        // 2026-06-16. If early-notice visibility is wanted later, build it as a
        // separate, de-duplicated, clearly-labeled section.)

        // Finalise: build drpPolicies / lrpPolicies from accumulated endorsements
        const mapped = Object.values(farmerMap).map(farmer => {
          const allLrp = farmer._allLrpEnds;

          // Build drpPolicies from _drpByPolicy — one entry per policy number, AIP preserved
          const drpPolicies = Object.entries(farmer._drpByPolicy).map(([pn, {aip: polAip, county: polCounty, ends}]) => {
            const sorted = ends.sort((a,b) => {
              const da = new Date(a.endDate||''), db = new Date(b.endDate||'');
              return (isNaN(da)?0:da) - (isNaN(db)?0:db);
            });
            return {
              id: farmer._primaryPolicyId,
              commodity: 'Milk',
              county: polCounty || farmer.county || '',
              policyNumber: pn,
              aip: polAip,
              cropYears: farmer._allCropYears.sort(),
              totalDeclaredMilk: sorted.reduce((s,e) => s + (Number(e.declaredMilkProd)||0), 0),
              totalInsuredValue: sorted.reduce((s,e) => s + (Number(e.expRevGuar)||0), 0),
              totalPremium:      sorted.reduce((s,e) => s + (Number(e.totalPremium)||0), 0),
              subsidyAmount:     sorted.reduce((s,e) => s + (Number(e.subsidy)||0), 0),
              producerPremium:   sorted.reduce((s,e) => s + (Number(e.producerPremium)||0), 0),
              endorsements: sorted,
            };
          });

          // Group LRP endorsements by policy number so AIP can be derived per-policy
          const lrpByPolicyNum = {};
          allLrp.forEach(e => {
            const pn = e._policyNumber || 'UNKNOWN';
            if (!lrpByPolicyNum[pn]) lrpByPolicyNum[pn] = [];
            lrpByPolicyNum[pn].push(e);
          });
          const lrpPolicies = Object.entries(lrpByPolicyNum).map(([pn, ends]) => ({
            id: farmer._primaryPolicyId,
            policyNumber: pn,
            aip: ends[0]?._aip || null,   // policy AIP — lets dashboard totals exclude prior-agent (non-book) LRP
            commodity: ends[0]?.commodity || 'Cattle',
            type: '', county: farmer.county || '',
            endorsements: ends,
          }));

          // Build prfPolicies from loaded prf_policies + prf_intervals tables
          // Match prf_policies rows to this farmer via policy_id (which is policyId for this farmer)
          // We need all policyIds for this farmer — stored in _allPolicyIds accumulator
          const farmerPrfPolicies = prfPolicyRows
            .filter(pp => farmer._allPolicyIds?.includes(pp.policy_id))
            .map(pp => ({
              id: pp.id,
              // Crop year of THIS PRF policy's parent row. Never fall back to
              // farmer.cropYear — that's the max across all products, which the
              // DRP July-1 reinsurance roll pushes a year ahead of the PRF book.
              cropYear: policyCropYearById[pp.policy_id] ?? null,
              // Carrier of the PRF policy itself, not the farmer's merged aip.
              aip: policyAipById[pp.policy_id] ?? null,
              // State this PRF policy is written in — the PRF Grid map's default.
              state: policyStateById[pp.policy_id] ?? null,
              policyNumber: pp.policy_number || farmer.policyNumber,
              county: pp.county || farmer.county,
              coverageLevel: pp.coverage_level,
              productivityFactor: pp.productivity_factor,
              totalAcres: pp.total_acres,
              totalLiability: pp.total_liability,
              totalPremium: pp.total_premium,
              netPremium: pp.net_premium,
              subsidy: pp.subsidy,
              billingDate: pp.billing_date,
              intervals: prfIntervalRows
                .filter(iv => iv.prf_policy_id === pp.id)
                .map(iv => ({
                  county: iv.county, gridId: iv.grid_id, intervalCode: iv.interval_code,
                  farmUnit: iv.farm_unit, insuredAcres: iv.insured_acres,
                  percentValue: iv.percent_value, liability: iv.liability,
                  productivityFactor: iv.productivity_factor, premium: iv.premium,
                  // The carrier's P11 share (sql-to-run: prf_intervals.insured_share).
                  // Absent until that column exists; the review then reads the share
                  // off the line liability (prfActuarial.reconcileLineFactors).
                  insuredShare: iv.insured_share ?? null,
                })),
            }));
          const prfPolicies = farmerPrfPolicies.length ? farmerPrfPolicies : farmer.prfPolicies || [];

          // Clean up internal accumulators before returning
          const { _drpByPolicy, _allLrpEnds, _allCropYears, _allPolicyNums, _primaryPolicyId, _allFarmerIds, _allPolicyIds, _has2025Prod, _missing2025Crops, _cropDisplayYear, _identityRank, _aipSet, ...clean } = farmer;
          // Carrier for the FARM. One carrier resolves to it; two or more resolve
          // to "Multiple" rather than to whichever policy the loader happened to
          // see first — that arbitrary pick silently credited an entire farm's
          // premium to one carrier on Book of Business by AIP. Anything needing a
          // real carrier must read it off the policy/coverage row (`a.aip`,
          // `p.aip`, `policyAipById`), which every rollup now does. "Multiple" is
          // a UI sentinel and is never written back — see saveFarmerToSupabase.
          const _aips = [..._aipSet];
          clean.aip = _aips.length > 1 ? "Multiple" : (_aips[0] || clean.aip || "NAU");
          clean.aips = _aips;
          // Anchor the crop/PRF display year to CURRENT_CROP_YEAR. Take the most
          // recent non-livestock policy year but never exceed the manual roll
          // anchor, so the forward-year crop SHELLS the RY file creates (empty
          // wheat/barley rows with null acres, seeded months before the season)
          // don't jump the whole farmer to a year with no real data. When Kevin
          // bumps CURRENT_CROP_YEAR the crop book advances with it. A
          // livestock-only farmer (no non-livestock policy) resolves to the
          // anchor. See LIVESTOCK_POLICY_TYPES.
          clean.cropYear = Math.min(_cropDisplayYear || CURRENT_CROP_YEAR, CURRENT_CROP_YEAR);
          clean.allFarmerDbIds = _allFarmerIds;
          clean.allPolicyIds   = _allPolicyIds || [];
          clean.policyId = _primaryPolicyId;
          clean.has2025Prod = _has2025Prod !== false;
          clean.missing2025Crops = _missing2025Crops || []; // default true if not explicitly false
          return { ...clean, drpPolicies, lrpPolicies, prfPolicies };
        });

        const visible = mapped.filter(f => !f.name?.startsWith("__diag_"));
        // Split out demo accounts so they NEVER reach book/dashboard/by-AIP totals.
        setDemoFarmers(visible.filter(isDemoFarmer));
        setFarmers(visible.filter(f => !isDemoFarmer(f)));
      } catch (err) {
        console.error("loadData failed:", err);
        // The error screen below (`if (dbError) return …`) replaces the whole
        // Dashboard, so there is nothing to keep visible — and never anything to
        // substitute. Whatever `farmers` already holds is left untouched.
        setDbError(`Database load error: ${err.message}. Nothing on this screen is the book — reload, or sign in again if your session has expired.`);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Detail lookups also include demo farmers (which are excluded from `farmers`)
  // so clicking a demo DRP card opens its detail. Aggregates keep using `farmers`.
  const lookupFarmers = useMemo(() => [...farmers, ...demoFarmers], [farmers, demoFarmers]);

  // selectedId is the policyId — unique per card even for same farmer with multiple policies
  const baseSelectedFarmer = lookupFarmers.find((f) => f.policyId === selectedId) || lookupFarmers.find((f) => f.id === selectedId);

  // Crop Review opens farm detail by passing a farmer_id as selectedId (not a
  // policyId), and the page's `nav` is "cropreview" — which isn't a FarmerView
  // section, so the detail body would render blank. Crop Review is a spring
  // row-crop QC page, so resolve its selection to the "spring" detail (drives
  // both the multi-policy merge below and the FarmerView section prop).
  // Field Mapper opens a card via selectedId while nav==="fields", but FarmerView
  // has no "fields" section (it would render a blank body). Resolve it to the
  // farmer's crop section — fall if they're fall-crop-only (e.g. wheat), else spring.
  const detailSection = nav === "cropreview" ? "spring"
    // Book is LOB-agnostic, so the row that was clicked tells us which detail
    // section to open (its own policy type) rather than the nav key.
    : nav === "book" ? (bookSection || "spring")
    : nav === "fields" ? (() => {
        const crops = (baseSelectedFarmer?.approvedYields || []).map(a => (a.crop || "").toUpperCase());
        const hasFall = crops.some(c => FALL_CROPS.includes(c));
        const hasSpring = crops.some(c => c && !FALL_CROPS.includes(c));
        return (hasFall && !hasSpring) ? "fall" : "spring";
      })()
    // Claims opens the unified card OVER the Claims page (nav stays "claims" so Back returns
    // to Claims, not a retired per-LOB page). "claims" isn't a FarmerView section — resolve to
    // the farmer's crop tab, or their line of business if they carry no crops.
    : nav === "claims" ? (() => {
        const t = baseSelectedFarmer?.policyTypes || [];
        const crops = (baseSelectedFarmer?.approvedYields || []).map(a => (a.crop || "").toUpperCase());
        const hasFall = crops.some(c => FALL_CROPS.includes(c));
        const hasSpring = crops.some(c => c && !FALL_CROPS.includes(c));
        if (t.includes("crop") || hasFall || hasSpring) return (hasFall && !hasSpring) ? "fall" : "spring";
        return t.includes("lrp") ? "lrp" : t.includes("drp") ? "drp" : t.includes("prf") ? "prf" : "spring";
      })()
    : nav;

  // DRP is cross-crop-year: each policy creates a separate farmerMap entry (one for -25, one for -26),
  // but the user-facing view should aggregate every DRP policy under one Sparta Farms entry. Mirrors
  // the merge in sectionFarmers (line ~8929) so clicking a merged DRP card opens all years of data,
  // not just the -25 entry that happened to win the id lookup above.
  // Merge a farmer-split object with all its same-name siblings so the unified
  // card's tabs cover EVERY line of business the customer carries, regardless of
  // which policy_number's split object we started from. Used by both the agent
  // detail card (via selectedFarmer) AND the portal My Coverage auto-open — the
  // latter previously passed a raw split object, so a Sparta grantee saw only the
  // one LOB (DRP) that split carried instead of Spring/PRF/LRP/DRP.
  const mergeFarmerSiblings = (baseSelectedFarmer) => {
    if (!baseSelectedFarmer) return baseSelectedFarmer;
    const normName = sortNameKey;   // order-insensitive: "RICE, CRAIG" == "CRAIG RICE"
    const myName = normName(baseSelectedFarmer.name);
    if (!myName) return baseSelectedFarmer;
    const siblings = lookupFarmers.filter(f => normName(f.name) === myName);
    if (siblings.length <= 1) return baseSelectedFarmer;

    // Unified farmer card: one DB farmer is split into several frontend cards
    // (one per policy_number — spring, wheat, DRP, LRP, PRF each have their own
    // number). The book list merges them by name; mirror that fully here so the
    // detail card's tabs cover EVERY line of business the customer carries, no
    // matter which policy id won the id lookup. (List merge: BookView ~12002.)
    const ayKey = (a) => `${(a.county||"").toUpperCase()}|${(a.crop||"").toUpperCase()}|${(a.type||"").toUpperCase()}|${(a.practice||"").toUpperCase()}`;
    const ppKey = (p) => `${(p.county||"").toUpperCase()}|${(p.crop||"").toUpperCase()}|${(p.type||"").toUpperCase()}`;
    const merged = {
      ...baseSelectedFarmer,
      approvedYields: [], ppEligibility: [], scoEcoEndorsements: [], claims: [],
      approvedYieldsByYear: {}, ppEligibilityByYear: {},
      lrpPolicies: [], prfPolicies: [],
      // P12 premium payments travel with the policy they were made on. Without
      // this the merged card kept only the starting sibling's payments, so a
      // fall-wheat policy carried on another sibling read "$0 paid".
      premiumPayments: [],
      policyTypes: [...(baseSelectedFarmer.policyTypes || [])],
      allPolicyIds: [...(baseSelectedFarmer.allPolicyIds || [])],
      allFarmerDbIds: [...(baseSelectedFarmer.allFarmerDbIds || [])],
      _allPolicyNums: [],
    };
    const drpByPn = {};
    const cropYears = new Set();
    // A policy's payments are taken from the first sibling that carries that
    // policy, so two siblings holding the same policy never count it twice,
    // while two real payments of the same amount on one policy both stay.
    const payPolicySeen = new Set();
    for (const s of siblings) {
      const sPays = s.premiumPayments || [];
      const sPids = new Set(sPays.map(pm => pm.policyId || `pn:${pm.policyNumber || ""}`));
      for (const pid of sPids) {
        if (payPolicySeen.has(pid)) continue;
        payPolicySeen.add(pid);
        sPays.forEach(pm => { if ((pm.policyId || `pn:${pm.policyNumber || ""}`) === pid) merged.premiumPayments.push(pm); });
      }
      if (s.cropYear) cropYears.add(s.cropYear);
      (s.approvedYields || []).forEach(ay => { if (!merged.approvedYields.some(e => ayKey(e) === ayKey(ay))) merged.approvedYields.push(ay); });
      (s.ppEligibility || []).forEach(pp => { if (!merged.ppEligibility.some(e => ppKey(e) === ppKey(pp))) merged.ppEligibility.push(pp); });
      // Per crop year — the year is the outer key, so two siblings' years never meet.
      for (const [y, rows] of Object.entries(s.approvedYieldsByYear || {})) {
        const t = (merged.approvedYieldsByYear[y] = merged.approvedYieldsByYear[y] || []);
        (rows || []).forEach(ay => { if (!t.some(e => ayKey(e) === ayKey(ay))) t.push(ay); });
      }
      for (const [y, rows] of Object.entries(s.ppEligibilityByYear || {})) {
        const t = (merged.ppEligibilityByYear[y] = merged.ppEligibilityByYear[y] || []);
        (rows || []).forEach(pp => { if (!t.some(e => ppKey(e) === ppKey(pp) && normalizePractice(e.practice) === normalizePractice(pp.practice))) t.push(pp); });
      }
      (s.scoEcoEndorsements || []).forEach(e => merged.scoEcoEndorsements.push(e));
      (s.claims || []).forEach(c => merged.claims.push(c));
      (s.lrpPolicies || []).forEach(p => merged.lrpPolicies.push(p));
      (s.prfPolicies || []).forEach(p => merged.prfPolicies.push(p));
      for (const p of (s.drpPolicies || [])) {
        const pn = p.policyNumber || 'UNKNOWN';
        if (!drpByPn[pn]) drpByPn[pn] = { ...p, endorsements: [] };
        drpByPn[pn].endorsements.push(...(p.endorsements || []));
      }
      (s.policyTypes || []).forEach(t => { if (!merged.policyTypes.includes(t)) merged.policyTypes.push(t); });
      (s.allPolicyIds || []).forEach(pid => { if (!merged.allPolicyIds.includes(pid)) merged.allPolicyIds.push(pid); });
      (s.allFarmerDbIds || []).forEach(fid => { if (!merged.allFarmerDbIds.includes(fid)) merged.allFarmerDbIds.push(fid); });
      if (s.policyNumber && !merged._allPolicyNums.includes(s.policyNumber)) merged._allPolicyNums.push(s.policyNumber);
      if (s.aip && s.aip !== merged.aip) merged.aip = "Multiple";
    }
    merged.drpPolicies = Object.values(drpByPn);
    merged._drpCropYears = [...cropYears].sort();
    if (merged._allPolicyNums.length > 1) merged.policyNumber = merged._allPolicyNums.join(" · ");
    return merged;
  };
  const selectedFarmer = mergeFarmerSiblings(baseSelectedFarmer);
  // ⚠ THE MAP MUST SAY WHICH CROP YEAR IT IS, AND IT MUST SAY THE YEAR IT ACTUALLY
  // USED. The fallback to DEFAULT_PRICES_BY_YEAR[2025] means a card whose own year
  // has no entry was being priced with 2025 money, with nothing on the object to
  // record it. Tagging the year USED rather than the year ASKED FOR turns that from
  // a silent substitution into a refused guarantee.
  const _pricesFor = (yr) => {
    const used = pricesByYear[yr] ? Number(yr) : 2025;
    const map = { ...(pricesByYear[yr] || DEFAULT_PRICES_BY_YEAR[2025]), ...dryBeanPrices };
    Object.defineProperty(map, "_cropYear", { value: used, enumerable: false });
    return map;
  };
  const activePrices = _pricesFor(selectedFarmer ? selectedFarmer.cropYear : CURRENT_CROP_YEAR);

  const saveDebounce = useRef({});
  async function resetFarmer(farmerId, section) {
    // Clear the specified section from Supabase and local state
    if (section === 'drp') {
      const farmerObj = farmers.find(f => f.id === farmerId);
      if (farmerObj) await resetFarmerDRP(farmerObj);
      setFarmers(prev => prev.map(f =>
        f.id === farmerId ? { ...f, drpPolicies: [], policyTypes: f.policyTypes.filter(t => t !== 'drp') } : f
      ));
    }
  }

  function updateFarmer(updated, opts = {}) {
    setFarmers(farmers.map((f) => f.id === updated.id ? updated : f));
    // Only auto-save when the caller explicitly requests it (persist:true).
    // This prevents page navigation and re-renders from overwriting DB data.
    if (!opts.persist) return;
    if (saveDebounce.current[updated.id]) clearTimeout(saveDebounce.current[updated.id]);
    saveDebounce.current[updated.id] = setTimeout(() => {
      saveFarmerToSupabase(updated).catch(err => console.warn('Auto-save failed:', err.message));
    }, 1500);
  }

  function handleImport(incoming, replace) {
    // Called with no args from Excel import — just trigger a page reload to refresh data
    if (!incoming) {
      window.location.reload();
      return;
    }
    const mergedResults = [];
    setFarmers((prev) => {
      const next = [...prev];
      incoming.forEach((nf) => {
        // LRP/LRP-Excel farmers have no policyNumber — match by normalized name instead.
        // PDF-imported farmers (crop/DRP/PRF) match by policyNumber+cropYear as before.
        const isLrpOnly = nf.policyTypes?.length === 1 && nf.policyTypes[0] === 'lrp' && !nf.policyNumber;
        const normName = (s) => (s||'').trim().toLowerCase();
        // For LRP, use fuzzy name matching: strip DBA/parenthetical suffixes, handle Last First vs First Last
        const normLrpName = (s) => {
          let n = (s||'').trim().toLowerCase();
          // Remove DBA and parenthetical suffixes: "EDWIN MORAN (EM LIVESTOCK)" → "edwin moran"
          n = n.replace(/\s*\(.*\)\s*/g, '').replace(/\s*dba\s+.*/i, '').trim();
          // Normalize "last, first" to "first last"
          if (n.includes(',')) {
            const parts = n.split(',').map(p => p.trim());
            if (parts.length === 2 && parts[1]) n = parts[1] + ' ' + parts[0];
          }
          return n.replace(/\s+/g, ' ').trim();
        };
        const idx = isLrpOnly
          ? next.findIndex((f) => {
              // Try exact match first
              if (normName(f.name) === normName(nf.name)) return true;
              // Fuzzy match: strip DBA/suffixes and compare
              if (normLrpName(f.name) === normLrpName(nf.name)) return true;
              // Check if one name contains the other (handles "Edwin Moran" matching "EDWIN MORAN (EM LIVESTOCK)")
              const a = normLrpName(f.name), b = normLrpName(nf.name);
              if (a && b && (a.includes(b) || b.includes(a))) return true;
              return false;
            })
          : next.findIndex((f) => f.policyNumber === nf.policyNumber && f.cropYear === nf.cropYear);
        if (idx >= 0) {
          // Merge lrpPolicies into existing farmer rather than replacing everything
          const base = next[idx];
          const merged = { ...base };
          if (nf.lrpPolicies?.length) {
            // For LRP-only imports (Excel bulk), REPLACE all endorsements — Excel is source of truth
            // For mixed imports (PDF), merge with dedup
            if (isLrpOnly) {
              // Full replace — Excel import is authoritative for all endorsements
              // But preserve actualEndValue/payableIndemnity from existing endorsements
              const existing = base.lrpPolicies?.[0]?.endorsements || [];
              const newEnds  = nf.lrpPolicies[0].endorsements || [];
              // Normalize date for matching: handle YYYY-MM-DD, MM/DD/YYYY, Date objects
              const normDate = (d) => {
                if (!d) return '';
                const s = String(d).trim();
                // MM/DD/YYYY → YYYY-MM-DD
                const mdy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (mdy) return `${mdy[3]}-${mdy[1]}-${mdy[2]}`;
                // Already YYYY-MM-DD
                if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
                return s;
              };
              // Try to carry over actualEndValue/payableIndemnity from old matching endorsements
              newEnds.forEach(ne => {
                const match = existing.find(e =>
                  normDate(e.endDate) === normDate(ne.endDate) &&
                  Number(e.coveragePrice) === Number(ne.coveragePrice) &&
                  Number(e.head) === Number(ne.head)
                );
                if (match) {
                  if (match.actualEndValue > 0) ne.actualEndValue = match.actualEndValue;
                  if (match.payableIndemnity > 0) ne.payableIndemnity = match.payableIndemnity;
                }
              });
              merged.lrpPolicies = [{ ...(base.lrpPolicies?.[0] || {}), ...nf.lrpPolicies[0], endorsements: newEnds }];
            } else {
              // PDF import — merge with dedup
              const existing = base.lrpPolicies?.[0]?.endorsements || [];
              const newEnds  = nf.lrpPolicies[0].endorsements || [];
              const normDate = (d) => {
                if (!d) return '';
                const s = String(d).trim();
                const mdy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (mdy) return `${mdy[3]}-${mdy[1]}-${mdy[2]}`;
                if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
                return s;
              };
              const combined = [...existing];
              newEnds.forEach(ne => {
                const dupeIdx = combined.findIndex(e => {
                  if (normDate(e.endDate) === normDate(ne.endDate) && Number(e.coveragePrice) === Number(ne.coveragePrice)) return true;
                  if (normDate(e.endDate) === normDate(ne.endDate) && Number(e.head) === Number(ne.head) && ne.head != null) return true;
                  return false;
                });
                if (dupeIdx >= 0) {
                  const old = combined[dupeIdx];
                  const mergedEnd = {};
                  const allKeys = new Set([...Object.keys(old), ...Object.keys(ne)]);
                  allKeys.forEach(k => {
                    if (k === 'actualEndValue') {
                      mergedEnd[k] = (old[k] && old[k] > 0) ? old[k] : (ne[k] || 0);
                    } else if (k === 'payableIndemnity') {
                      mergedEnd[k] = (old[k] && old[k] > 0) ? old[k] : (ne[k] || null);
                    } else {
                      mergedEnd[k] = (ne[k] != null && ne[k] !== 0) ? ne[k] : (old[k] ?? ne[k]);
                    }
                  });
                  combined[dupeIdx] = mergedEnd;
                } else {
                  combined.push(ne);
                }
              });
              merged.lrpPolicies = [{ ...(base.lrpPolicies?.[0] || {}), ...nf.lrpPolicies[0], endorsements: combined }];
            }
            merged.policyTypes = [...new Set([...(base.policyTypes||[]), 'lrp'])];
          }
          const final = replace ? nf : merged;
          next[idx] = final;
          mergedResults.push(final);
        } else {
          next.push(nf);
          mergedResults.push(nf);
        }
      });
      return next;
    });
    incoming.forEach((nf) => {
      if (!pricesByYear[nf.cropYear]) setPricesByYear((p) => ({ ...p, [nf.cropYear]: { CORN:0, SOYBEANS:0, "DRY BEANS":0, WHEAT:0 } }));
    });
    const FALL_CROP_NAMES = ['WHEAT'];
    const allIncomingAreFallOnly = incoming.length > 0 && incoming.every(f =>
      f.approvedYields?.length > 0 &&
      f.approvedYields.every(y => FALL_CROP_NAMES.includes((y.crop || '').toUpperCase())) &&
      !f.ppEligibility?.length && !f.drpPolicies?.length && !f.lrpPolicies?.length
    );
    setNavPersist(allIncomingAreFallOnly ? "fall" : "spring");
    return mergedResults;
  }

  async function saveYearPrices(year, prices) {
    setPricesByYear((prev) => ({ ...prev, [year]: prices }));
    try {
      const rows = Object.entries(prices).map(([crop, price]) => ({ crop_year: year, crop, price_guarantee: price }));
      await sbUpsert("price_settings", rows);
    } catch(err) { console.error("Failed to save prices:", err); }
  }

  async function saveDryBeanPrices(prices) {
    setDryBeanPrices(prices);
    try {
      const rows = Object.entries(prices).map(([type, price]) => ({
        crop_year: 0, // global — not crop-year specific
        crop: `DRYBEAN_${type}`,
        price_guarantee: price,
      }));
      await sbUpsert("price_settings", rows);
    } catch(err) { console.error("Failed to save dry bean prices:", err); }
  }

  if (loading) return (
    <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans','Helvetica Neue',sans-serif" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:28, marginBottom:12 }}>🍀</div>
        <div style={{ fontSize:14, color:"#4a6e4a", fontWeight:600 }}>Loading data…</div>
      </div>
    </div>
  );

  if (dbError) return (
    <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans','Helvetica Neue',sans-serif" }}>
      <div style={{ textAlign:"center", maxWidth:500, padding:32 }}>
        <div style={{ fontSize:28, marginBottom:12 }}>⚠️</div>
        {/* NOT "Database Load Error". Three of bookReadFailureMessage's four
            branches are a dead SESSION, and heading those with a database fault
            sends the reader looking in the wrong place — which is exactly what a
            page showing four fabricated growers already did on 2026-10-11. This
            heading is true on every branch, including the catch path. */}
        <div style={{ fontSize:16, fontWeight:800, color:"#c0392b", marginBottom:12 }}>Your customer list is not loaded</div>
        <div style={{ fontSize:12, fontVariantNumeric: "tabular-nums", color:"#c0392b", background:"#fdecea", padding:"12px 16px", borderRadius:8, textAlign:"left", marginBottom:16 }}>{dbError}</div>
        <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
          <button onClick={() => { setDbError(null); window.location.reload(); }} style={{ padding:"10px 24px", background:"#2d4a2d", color:"#fff", border:"none", borderRadius:7, fontWeight:800, fontSize:13, cursor:"pointer" }}>Retry</button>
          {/* The message above tells the reader to sign in again in three of its
              four branches, and a reload re-runs the same dead session. */}
          <button onClick={() => { supabaseSignOut(); window.location.reload(); }} style={{ padding:"10px 24px", background:"#fff", color:"#2d4a2d", border:"1.5px solid #2d4a2d", borderRadius:7, fontWeight:800, fontSize:13, cursor:"pointer" }}>Sign in again</button>
        </div>
      </div>
    </div>
  );

  // ── FARMER PORTAL render path ──────────────────────────────────────────────
  // Separate branch from the agent Dashboard render below (which is untouched).
  // Reuses the same RLS-scoped `farmers` data + leaf views, with portal-only
  // chrome and a nav limited to the user's granted lines of business.
  if (portalMode || previewAs) {
    const _pm = portalMode || previewAs;
    const { grants = [], canViewMi = false, canViewElevator = false, canSetAlerts = false, canView578 = false, bookWide578 = false, canViewModelFarm = false, canViewCashTargets = false, canView426 = false, role = null, displayName, homeLat = null, homeLon = null, homeLabel = null } = _pm;
    // Preview no longer needs client-side scoping: it runs the FARMER's own
    // session, so `farmers` arrives pre-scoped by their RLS exactly as it does
    // for them. `isPreview` now only drives the orange chrome and the analytics
    // opt-outs below.
    const isPreview = !portalMode && !!previewAs;
    // `can_view_578` / underwriter = book-wide FILES, and nothing else.
    //
    // It used to mean "book viewer": whoever held it saw the ENTIRE book — every
    // farmer, every LOB, Claims, Field Mapper, Home. That was harmless for Mandy,
    // an underwriter with no farm grants doing QC, so nobody noticed the flag was
    // carrying two meanings. Giving it to Daniele — a LIMITED agent with 10 farm
    // grants — surfaced the difference: she got the whole book's field boundaries
    // and a Home page of book-wide aggregates on top of her own customers.
    //
    // The two meanings are now separate. `bookFiles` governs File Management only.
    // Every other screen is scoped to the user's own farm grants, exactly as it is
    // for any limited agent — so Daniele keeps FULL access to her 10 customers
    // (Home, Coverage, Claims, Field Mapper) and sees nothing of the other 240.
    // ⚠ AND "BOOK-WIDE FILES" IS NO LONGER THE FLAG EITHER (Kevin, 2026-09-11):
    // "capability to work the 578 process should not automatically mean book-wide
    // farmer visibility". Measured that day, the flag let Daniele read all 272
    // FSA-578s, all 304 document records and all 747 document files — the book,
    // not her 10 customers — through farmer_578s, farmer_documents, storage and
    // farmers_578_queue. Book-wide is now its own EXPLICIT permission,
    // portal_users.book_wide_578, granted by deliberate DB change like the flag
    // itself; neither the flag nor the underwriter ROLE implies it any more.
    // A flag holder without it works the 578 process for her own grants.
    const bookFiles = bookWide578;
    const grantIds = grants.map(g => g.farmer_id).filter(Boolean);
    // A real 578 holder's RLS over-returns (viewer_farmers_read_all), so the
    // non-file screens are still scoped here for them.
    //
    // Preview is deliberately NOT in this condition any more. It used to be,
    // because the admin's token fetched the whole book and someone had to trim
    // it. Now preview holds the farmer's own session, so trimming again would
    // apply a filter the FARMER doesn't have — preview would show less than they
    // see, and worse, it would mask an RLS policy that over-returns. The point of
    // the diagnostic is that whatever RLS hands back is what you look at.
    const scopeIds = new Set(grantIds.filter(Boolean));
    const needsClientScope = bookFiles;
    const inScope = (f) => scopeIds.has(f.id) || (f._allFarmerIds || []).some(id => scopeIds.has(id));
    // ⚠ A DEMO FARM IS EXCLUDED FROM THE BOOK, NOT FROM THE PERSON GRANTED IT.
    //
    // `farmers` deliberately excludes demo accounts — loadData does
    // `setFarmers(visible.filter(f => !isDemoFarmer(f)))` and keeps them in
    // `demoFarmers` — because a clone of a real customer left in the book counts
    // its premium twice. That is right, and buildPremiumFacts excludes them again
    // on its own side. Farmer detail re-adds them explicitly (`lookupFarmers`),
    // which is the pattern this follows.
    //
    // But the PORTAL derived from `farmers` too, so a portal user GRANTED a demo
    // farm could never see it. Measured 2026-09-07: michael.meisenzahl@gmail.com
    // holds 7 active grants including SPARTAN INSURANCE FARMS, RLS returns all 7
    // farmers / 19 policies / 116 endorsements to his own session, and the
    // operation picker offered SIX. Kevin: "I enabled him to see Spartan insurance
    // farms but it's not showing."
    //
    // ⚠ THIS CANNOT WIDEN THE AGENT APP. It is inside the portal branch, and a
    // demo farm still reaches a reader only through `inScope` / RLS — i.e. only
    // where somebody deliberately granted it. `farmers` is untouched, so every
    // agent aggregate stays demo-free by construction rather than by a filter
    // each one has to remember.
    const portalSource = demoFarmers.length ? [...farmers, ...demoFarmers] : farmers;
    const portalFarmers = needsClientScope ? portalSource.filter(inScope) : portalSource;
    // Screens that fetch their own rows (Field Mapper, 578) filter client-side on
    // these ids rather than trusting the query to have been scoped.
    const restrictIds = needsClientScope ? [...scopeIds] : undefined;
    // The John Deere screens read through service-role handlers, where RLS does
    // nothing and the client-scope shortcut above doesn't apply: the request must
    // NAME the farms every time, preview or not. Always the concrete list.
    const portalGrantIds = [...scopeIds];
    // ── SELECTED OPERATION — a NARROWING, applied strictly after authorization ──
    // `portalFarmers` above is the authorized set. Choosing an operation can only
    // ever remove rows from it, so a tampered value cannot reach a farm the user
    // was not granted: an unrecognised key matches no row and the guard below
    // falls back to the FULL authorized set rather than to an empty or wider one.
    // The client-side value is a VIEW PREFERENCE, never an authorization input -
    // RLS and the grant-derived scopeIds remain the only things that decide reach.
    const portalOps = portalOperations(portalFarmers);
    const selectedOpRows = selectedOpKey
      ? portalFarmers.filter(f => portalOpKey(f) === selectedOpKey)
      : [];
    const opScopedFarmers = selectedOpRows.length ? selectedOpRows : portalFarmers;
    // Ids for the service-role screens must narrow with the operation too, and
    // must still be a SUBSET of the grant - never the raw selection.
    const opScopedIds = [...new Set(opScopedFarmers
      .flatMap(f => [f.id, ...((f._allFarmerIds) || [])])
      .filter(id => id && scopeIds.has(id)))];
    // File Management is the ONE screen that legitimately spans the book, and only
    // for a flag holder. Everyone else works their own customers' files.
    // Prefer the narrowed farmers_578_queue list (id/name/county only); fall back
    // to `farmers` when the view isn't there yet, so this is safe to deploy
    // before or after sql/015.
    const fileFarmers = bookFiles ? (fileFarmerList.length ? fileFarmerList : farmers) : portalFarmers;
    const lobSet = new Set();
    grants.forEach(g => ((g.permissions && g.permissions.lobs) || []).forEach(l => lobSet.add(l)));
    const SECTION_META = [
      { key:"spring", icon:"🌱", label:"Spring Crops" },
      { key:"fall",   icon:"🌾", label:"Fall Crops" },
      { key:"drp",    icon:"🥛", label:"DRP" },
      { key:"lrp",    icon:"🐄", label:"LRP" },
      { key:"prf",    icon:"🌧️", label:"PRF" },
    ];
    // Lines of business come from the user's own grants — no longer force-filled
    // for a 578 holder, which is what put every LOB on Daniele's nav.
    // Scope the granted coverage lobs down to what the customer ACTUALLY carries.
    // A portal grant can over-include a coverage (default grants add PRF/Spring/etc.)
    // the farmer doesn't hold, which would surface an irrelevant tab — e.g. Laier
    // Dairy (DRP/LRP only) was granted PRF and saw the PRF Grid in Markets. Mirror
    // FarmerView's `avail` detection, union across the user's farms, and drop any
    // granted coverage lob with no data behind it. `grain` is a tool grant (not
    // coverage), so it stays purely grant-driven.
    // `carried` is hoisted out of the block below because it answers TWO questions,
    // not one: which coverage LOBs to show, and (further down) whether this account
    // has any crop ground at all.
    const carried = new Set();
    {
      const isFallCrop = c => FALL_CROPS.includes((c || "").toUpperCase());
      (portalFarmers || []).forEach(f => {
        const ay = f.approvedYields || [], pe = f.ppEligibility || [], types = f.policyTypes || [];
        if (ay.some(a => a.crop && !isFallCrop(a.crop)) || pe.some(p => p.crop && !isFallCrop(p.crop))) carried.add("spring");
        if (ay.some(a => isFallCrop(a.crop)) || pe.some(p => isFallCrop(p.crop))) carried.add("fall");
        if ((f.drpPolicies || []).some(p => (p.endorsements || []).length) || types.includes("drp")) carried.add("drp");
        if ((f.lrpPolicies || []).some(p => (p.endorsements || []).length) || types.includes("lrp")) carried.add("lrp");
        if ((f.prfPolicies || []).length > 0 || types.includes("prf")) carried.add("prf");
      });
      [...lobSet].forEach(k => { if (k !== "grain" && !carried.has(k)) lobSet.delete(k); });
    }
    const lobSections = SECTION_META.filter(s => lobSet.has(s.key));
    // Farm pages follow FARM GRANTS only. Holding the 578 flag no longer conjures
    // farms a user wasn't granted (that's what gave Mandy, who has zero grants, a
    // Home page of the whole book).
    const hasFarms = grants.length > 0;
    // ...but the CROP-FIELD pages need a second question answered: does this farm
    // have any crop ground? `hasFarms` alone put Field Mapper, Yield Analytics and
    // Field Progress on Daryl Keem's nav — a dairy carrying DRP + LRP and nothing
    // else, with 0 policy_fields, 0 crop_coverage and no John Deere connection.
    // Field Mapper opened empty; the two JD pages opened on a connect panel for an
    // operation with no acres to connect.
    //
    // This is the Laier Dairy fix's blind spot, not a regression of it. The
    // `carried` narrowing above prunes `lobSet`, which drives the Coverage tabs,
    // the Quoters hub and Markets — it never reached these three, because they sit
    // outside the LOB list entirely. Same class of bug, one gate over.
    //
    // `carried` is the right test and it is already computed: it is read off the
    // farmer's OWN data (approved yields / PP eligibility / policy types), not off
    // the grant, so a grant that over-includes `spring` can't conjure the pages
    // back. A dairy that also insures corn silage keeps all three, because silage
    // puts `spring` in `carried`.
    //
    // Kevin's call, 2026-08-14: a livestock-only policy should not see Field Mapper
    // or Yield Analytics. Field Progress rides along — same gate, same crop-field
    // class, and it is the JD planting/harvest page for ground this account has none
    // of. Note this DOES narrow the "offer the JD pages to the unconnected too"
    // rule below: that reasoning holds for a crop farm that hasn't connected yet,
    // not for a dairy with no crop acres in the book.
    const hasCropGround = carried.has("spring") || carried.has("fall");
    // Coverage is one unified card now (all LOBs as tabs), not a page per LOB.
    // Quoters/Markets are the consolidated hubs, scoped to the granted lobs. Kept
    // `lobSections` computed above (still feeds the coverage-tab lobFilter + which
    // hubs to offer). hasQuoter/hasMarket decide which hubs appear.
    // ⚠ THE QUOTERS ARE A SALES TOOL, NOT A COVERAGE SURFACE — so they read the
    // GRANTED lobs, not the `carried` narrowing (Kevin, 2026-09-07).
    //
    // This read `lobSet`, which the `carried` filter above prunes to the LOBs the
    // farm actually holds a policy for. Chris Dawson's eight operations are granted
    // spring/fall/prf/lrp/drp on ALL EIGHT and his page_access explicitly names
    // "quoters" — two deliberate admin ticks — and the nav still had no Quoters
    // entry, because he carries crop + PRF and no DRP/LRP policy, so `carried`
    // deleted both and `hasQuoter` went false. It rendered as a save that did not
    // take: exactly the market_tabs defect this file already records, one page over.
    //
    // Widening is safe for the same reason it is safe for Markets, and the reason is
    // stronger here: the DRP and LRP quoters read PUBLIC RMA rate tables and no
    // farmer data at all (QuotersHub says so at its own definition). LRP Profit Calc
    // does take `farmers`, but that is `opScopedFarmers` — this user's OWN
    // operations, RLS-scoped — so a grower with no LRP gets an empty calculator, not
    // somebody else's book.
    //
    // ⚠ AND A QUOTER IS EXACTLY WHAT SOMEBODY WHO DOES NOT CARRY THE COVERAGE WANTS.
    // Narrowing it to what they already own means the one grower who might buy DRP
    // is the one grower who cannot price it. That is the Markets argument
    // (2026-08-13) applied to the tool that sells the thing.
    //
    // The comment below used to end "...so widening here never conjures a DRP quoter
    // for someone who carries neither." That was the deliberate behaviour and this is
    // Kevin changing it; it is recorded rather than quietly reversed.
    //
    // MARKETS IS DELIBERATELY WIDER THAN COVERAGE (Kevin, 2026-08-13): a grower
    // who doesn't carry DRP or LRP should still be able to read the milk and
    // cattle markets. So the market hub reads the GRANTED lobs, before the
    // `carried` narrowing above deletes the ones with no policy behind them.
    //
    // Safe because those five views read NO farmer data at all — MilkMIView,
    // DRPPerformanceView, DMCView and CattleMIView take no farmer props, and
    // LRPPerformanceView accepts `farmers`/`mode` but never reads either. That
    // is the same reasoning that leaves Ag Calculators and Weather ungated:
    // public market data, nothing to scope. The grant is still the switch, so
    // Kevin can turn it off per user in the portal permission editor.
    //
    // ONLY drp/lrp widen — and since 2026-08-21 they are the only market LOBs
    // there are. The PRF Grid used to need a hand-written exception here
    // (PRFGridMapView reads `farmers`, so an uncarried PRF tab was the exact
    // Laier Dairy symptom the `carried` filter exists to fix); it now has its
    // own page, gated on `lobSet` like every other coverage surface, so the
    // exception is gone rather than restated. Coverage tabs, the Quoters hub,
    // the PRF Grid page and the nav all read `lobSet`, so widening here never
    // conjures a DRP quoter or a DRP coverage card for someone who carries
    // neither.
    // ONE widened set, computed once and read by BOTH the market hub and the quoter
    // hub. It was declared here and used only for Markets; `hasQuoter` above now
    // needs the same answer, and two copies of "granted, before the carried
    // narrowing" is how the two hubs come to disagree about one grower.
    const marketLobSet = new Set(lobSet);
    grants.forEach(g => ((g.permissions && g.permissions.lobs) || [])
      .forEach(l => { if (l === "drp" || l === "lrp") marketLobSet.add(l); }));
    // The quoter hub's own filter. Same set, named for what it gates so a reader of
    // the QuotersHub call site is not sent to a variable called "market".
    const quoterLobSet = marketLobSet;
    const hasQuoter = quoterLobSet.has("drp") || quoterLobSet.has("lrp");
    // PER-USER MARKET SELECTION (portal_users.market_tabs). NULL/absent means
    // FOLLOW COVERAGE — what every user had before the column existed — so this
    // changes nobody's view until an admin sets an explicit list.
    const marketTabSet = Array.isArray(_pm.marketTabs) ? new Set(_pm.marketTabs) : null;
    // ONE rule decides the tabs, and the nav asks it the same question the hub
    // will answer. Deriving "should Markets appear" separately is how a user ends
    // up with a Markets item that opens an empty page — every tab filtered out.
    const marketTabsForUser = marketHubTabs(marketLobSet, marketTabSet);
    const hasMarket = marketTabsForUser.length > 0;
    // One nav for everyone, driven by farm grants. A 578 holder gets these pages
    // on THEIR OWN customers exactly like any limited agent — Daniele's 10 farms
    // give her Home, Coverage, Claims and Field Mapper for those 10 — plus File
    // Management, which is the single screen the flag widens to the whole book.
    // Mandy holds the flag with no grants, so she lands on File Management alone.
    // PAGE ALLOWLIST (portal_users.page_access, sql/049). NULL means follow the
    // gates below — what every user had before the column — so this is inert
    // until an admin sets a list. See portalPagesFor / PORTAL_PAGES for what an
    // allowlist may and may not reach.
    const pageSet = Array.isArray(_pm.pageAccess) ? new Set(_pm.pageAccess) : null;
    const portalNavBase = [
      // TWO Home pages, and they are mutually exclusive by construction.
      // A user WITH farms gets PortalHomeView — their own farm summary, unchanged.
      // A user with NO farms (a limited staff member) can be granted the SILAS
      // market briefing instead: same house icon, same "Home" label, and the one
      // page in this app that tools/check-home-safe.js asserts reads no book
      // data at all. Splitting the keys keeps each on the right side of the
      // open/book line above; a single key would have been one page in two
      // classes, which the allowlist rule could not have reasoned about.
      ...(hasFarms ? [{ key:"home", icon:"🏠", label:"Home" }] : []),
      ...(!hasFarms ? [{ key:"briefing", icon:"🏠", label:"Home" }] : []),
      // `short` is the phone bottom-tab label. Set it wherever the first word of
      // the full label isn't the meaningful one — the bar is ~9px and a bad
      // shortening ("MY", "AG", "FILE") names nothing.
      ...(lobSections.length ? [{ key:"coverage", icon:"📇", label:"My Coverage", short:"Coverage" }] : []),
      ...(hasQuoter ? [{ key:"quoters", icon:"🧮", label:"Quoters" }] : []),
      ...(hasMarket ? [{ key:"markets", icon:"📊", label:"Markets" }] : []),
      // PRF Grid — its own page from 2026-08-21, no longer a Markets tab. Gated
      // on `lobSet`: the PRF grant NARROWED by what the farm actually carries,
      // the same gate the coverage tabs use, NOT the widened `marketLobSet`.
      // That is the Laier Dairy rule — a default grant handing PRF to a DRP/LRP
      // dairy must not put a PRF page on their nav — and it is now enforced by
      // the ordinary gate instead of by an exception inside the Markets hub.
      // PRF Coverage is no longer its own nav item — it is the second tab of My
      // Coverage → PRF, beside the rainfall view it always belonged with. The KEY
      // survives as a legacy route (below) so a saved link, a deep link, or an
      // existing page_access list naming "prfreview" still lands somewhere real
      // rather than on the first page in the nav.
      ...(lobSet.has("prf") ? [{ key:"prfgrid", icon:"🌧️", label:"PRF Grid", short:"Grid" }] : []),
      ...(hasFarms ? [{ key:"claims", icon:"💰", label:"Claims" }] : []),
      ...(hasCropGround ? [{ key:"fields", icon:"🌾", label:"Field Mapper", short:"Fields" }] : []),
      // The two John Deere pages. Offered to anyone with CROP GROUND rather than to
      // the connected only: a grower who ISN'T connected is exactly who needs to
      // find these, and both screens open on the connect panel instead of an empty
      // page. That argument is about connection state, not about acres — a
      // livestock-only account has no acres either way, so it gates on
      // `hasCropGround`, not `hasFarms`. Everything behind them is caller-scoped
      // server-side (jd-yieldanalytics / jd-claimwatch → gateScoped) AND asked for
      // by farm id.
      ...(hasCropGround ? [{ key:"yieldanalytics", icon:"🎯", label:"Yield Analytics", short:"Yield" }] : []),
      ...(hasCropGround ? [{ key:"fieldprogress", icon:"🌱", label:"Field Progress", short:"Progress" }] : []),
      ...(lobSet.has("grain") ? [{ key:"grain", icon:"🌽", label:"Grain Marketing", short:"Grain" }] : []),
      // Ungated on purpose: the calculators read no farmer data at all, so there
      // is no grant to scope them by. Every portal user gets them.
      { key:"agcalc", icon:"📐", label:"Ag Calculators", short:"Calcs" },
      // Same reasoning: the Weather page is coordinates in, rainfall out. The locations
      // it saves are the USER'S own (weather_locations is scoped by created_by, so
      // one farmer never sees another's, and neither does an agent), and the
      // forecast endpoint reads no book data. So it needs no grant either.
      { key:"rainwatch", icon:"🌦️", label:"Weather", short:"Weather" },
      ...(canSetAlerts ? [{ key:"alerts", icon:"🔔", label:"Price Alerts", short:"Alerts" }] : []),
      // File Management: book-wide ONLY for the explicit permission (book_wide_578,
      // sql/069), otherwise scoped to their own customers for an agent with farm
      // grants — the database now scopes a flag holder to her grants too. Named
      // the same as the agent app's nav so everyone means one thing by it.
      ...(PORTAL_STAFF_ROLES.has(role) && (bookFiles || (role === "agent" && hasFarms)) ? [{ key:"fsa578", icon:"📋", label:"File Management", short:"Files" }] : []),
      ...(canViewMi ? [{ key:"market", icon:"📈", label:"Market Intelligence", short:"Intel" }] : []),
      // Cash Targets — the agency's working-order book across every grower, so
      // it is "agency" class and rides its OWN flag rather than a farm grant or
      // the page allowlist. can_view_cash_targets drives both this nav entry and
      // the grain_cash_targets policy in sql/049, so the screen and the rows
      // arrive together or not at all.
      ...(canViewCashTargets ? [{ key:"cashtargets", icon:"🎯", label:"Cash Targets", short:"Targets" }] : []),
      // Model Farm retired 2026-07-29. The can_view_model_farm column and the
      // permission checkbox stay so no grant data is destroyed, but the screen is
      // no longer reachable from either app. See the retirement note in the
      // ModelFarmView block.
    ];
    // The "open" pages an allowlist may ADD to the nav — the ones that read no
    // farmer data. Everything else can only be REMOVED by it.
    const portalOpenCatalog = [
      { key:"briefing",  icon:"🏠", label:"Home" },
      { key:"markets",   icon:"📊", label:"Markets" },
      { key:"market",    icon:"📈", label:"Market Intelligence", short:"Intel" },
      { key:"agcalc",    icon:"📐", label:"Ag Calculators", short:"Calcs" },
      { key:"rainwatch", icon:"🌦️", label:"Weather", short:"Weather" },
    ];
    const portalNav = portalPagesFor(portalNavBase, pageSet, portalOpenCatalog);
    const allowedKeys = portalNav.map(n => n.key);
    const cur = allowedKeys.includes(nav) ? nav : (allowedKeys[0] || "market");
    // A farmer landing on a page they aren't granted — via an emailed deep link,
    // a bookmark, or a stale sessionStorage nav — is demand we can't otherwise
    // see. It says "turn this on for them" or "they expected this to exist".
    // Deliberately NOT a hook: this whole branch is conditional (portalMode /
    // previewAs), and adding a hook inside it would break hook order the moment
    // an admin toggles preview. The dedupe set makes the render-time call safe.
    if (nav && allowedKeys.length && !allowedKeys.includes(nav)) trackPortalBlockedNav(nav, cur);
    const logout = () => { try { supabaseSignOut(); } catch (e) {} window.location.reload(); };
    // Drilling from a list into a single farm is the clearest "engaged, not just
    // glancing" signal we have, so it gets its own action event.
    const portalSelect = (id) => { trackPortalAction("open_farm", { screen: cur }); setSelectedId(id); };

    // ── "ALL OPERATIONS" IS HOME-ONLY, AND THAT IS ENFORCED, NOT LABELLED ──────
    // A claim, a document, a quote, a production record and a recommendation are
    // all farm-specific. Showing two insureds' rows on one of those without the
    // reader having chosen an operation is the same merge this change removes,
    // one surface further out — so the page ASKS rather than aggregates. Home is
    // deliberately excluded: it is the surface that exists to show every
    // operation, and a user who has just signed in must see both immediately
    // rather than pick blind before seeing anything.
    const OP_AGNOSTIC_PAGES = new Set(["home", "briefing", "markets", "market", "agcalc", "rainwatch", "prices"]);
    const needsOpChoice = portalOps.length > 1 && !selectedOpKey && !OP_AGNOSTIC_PAGES.has(cur);
    // ⚠ PICKING AN OPERATION ON HOME CHANGED NOTHING ON SCREEN.
    //
    // `selectedOpKey` scopes every farm-specific page, and Home is deliberately
    // NOT one of them — it is the surface that exists to show every operation, so
    // it renders `portalFarmers` whole and never reads the key. The sidebar
    // dropdown therefore set state that produced no visible change, on the one
    // page where it sits directly above a grid of operation cards each carrying a
    // "View operation →" button that DOES work. Two controls, one intent, one of
    // them silently inert. Reported by Kevin 2026-09-07 on Chris Dawson's eight
    // operations, the day before that account is demonstrated.
    //
    // So the dropdown does what the card button does: choose the operation AND go
    // to the page that can show it. It is scoped to Home on purpose — on a
    // farm-specific page the choice is already reflected where the reader is
    // looking, and yanking them to My Coverage from Claims would be a second,
    // worse surprise. `coverage` is the card button's own destination; if an
    // allowlist has removed it, fall back to the first page this user actually has
    // rather than navigating somewhere their nav does not contain.
    const chooseOperation = (key) => {
      setSelectedOpKey(key || null);
      setSelectedId(null);
      if (!key || cur !== "home") return;
      const target = allowedKeys.includes("coverage") ? "coverage"
        : allowedKeys.find(k => !OP_AGNOSTIC_PAGES.has(k));
      if (target) setNavPersist(target);
    };

    let content;
    if (loading) {
      content = <div style={{ textAlign:"center", padding:60, color:"#60735f" }}>Loading your account…</div>;
    } else if (cur === "home") {
      // onOpenMarkets is passed ONLY when this customer's nav offers Markets, so
      // Home's "More market news" link can never point at a page they lack.
      content = <PortalHomeView farmers={portalFarmers} prices={activePrices} isMobile={isMobile} displayName={displayName} homeLat={homeLat} homeLon={homeLon} homeLabel={homeLabel} onSelectOperation={(k) => { setSelectedOpKey(k); setNavPersist("coverage"); }}
        onOpenMarkets={allowedKeys.includes("markets") ? () => setNavPersist("markets") : null} />;
    } else if (needsOpChoice) {
      // Farm-specific surface, several authorized operations, none chosen. Ask.
      content = (
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ color:"#1a2e1a", fontFamily:"'Lora',serif", fontSize: isMobile?21:25, margin:"0 0 6px" }}>Choose an operation</h2>
          <div style={{ fontSize:13, color:"#60735f", marginBottom:14 }}>
            This page shows one operation at a time, so its figures always belong to a single insured.
            Your account summary for all {portalOps.length} operations is on Home.
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {portalOps.map(o => (
              <button key={o.key} onClick={()=>{ setSelectedOpKey(o.key); setSelectedId(null); }}
                style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px",
                         borderRadius:12, border:"1px solid #dde7dd", background:"#fff", cursor:"pointer",
                         fontFamily:"inherit", textAlign:"left" }}>
                <span style={{ fontWeight:800, color:"#1a2e1a", fontSize:15 }}>{o.name}</span>
                <span style={{ color:"#2d6a2d", fontWeight:700 }}>Open →</span>
              </button>
            ))}
          </div>
        </div>
      );
    } else if (cur === "briefing") {
      // The SILAS market briefing. `allowedNav` is REQUIRED here, not cosmetic:
      // this page's quick-access row links straight into All Customers, Claims,
      // Field Mapper, Grain Marketing and Quoters in the agent app, and an
      // unfiltered copy would put those on a staff member's screen as dead ends
      // at best. Passing the viewer's own allowedKeys means a link can only ever
      // point somewhere they already have.
      content = <SilasHomeView isMobile={isMobile} displayName={displayName || getCurrentUserEmail() || ""}
        allowedNav={allowedKeys} onNav={(k)=>{ setSelectedId(null); setNavPersist(k); }} />;
    } else if (cur === "cashtargets") {
      // farmers is the RLS-scoped list, so a staff member with no grants gets []
      // and the form's farmer picker is simply empty — they type the name off
      // the paper ticket, which the form already supports ("a grain customer
      // need not be in the insurance book"). canDelete is admin-only to match
      // sql/049, which grants staff select/insert/update and NOT delete.
      content = <GrainTargetsView farmers={opScopedFarmers} isMobile={isMobile} canDelete={isCurrentUserAdmin()} />;
    } else if (cur === "grain") {
      // Full farmer-editable grain tool, scoped to their granted farm(s) via the RLS-filtered `farmers`.
      content = <GrainMarketingView farmers={opScopedFarmers} isMobile={isMobile} mode="portal" />;
    } else if (cur === "coverage") {
      // Unified card. portalFarmers is split per policy_number, so dedupe by name
      // for the picker (the card re-merges the siblings). A grantee with ONE
      // operation opens straight in; several operations get a short picker. Tabs
      // are lobFilter-scoped so a scoped grantee only sees granted coverages.
      // ONE canonical operation resolution, shared with Home - see portalOperations().
      // This was an inline `cvNorm`, which is exactly how Home came to hold a
      // different idea of who the user is: two readings of one question.
      const uniqueFarms = portalOperations(opScopedFarmers).map(o => o.rows[0]);
      // Merge the single farm's split siblings so its card shows ALL granted LOBs
      // (Spring/Fall/PRF/LRP/DRP), not just the LOB the deduped split carried.
      const onlyFarmer = uniqueFarms.length === 1 ? mergeFarmerSiblings(uniqueFarms[0]) : null;
      const shownFarmer = selectedFarmer || onlyFarmer;
      // ⚠ BACK WAS A DEAD CONTROL, AND THE FALLBACK ON THE LINE ABOVE IS WHY.
      //
      // `onBack` cleared `selectedId` only. The moment an operation is chosen — from
      // the Home card, or from the sidebar dropdown — `opScopedFarmers` narrows to
      // that one operation, `uniqueFarms.length === 1`, and `onlyFarmer` becomes
      // non-null. So clearing `selectedId` fell straight through the `||` into the
      // SAME farmer and re-rendered the identical card. The button was visible,
      // clickable, and did nothing. Reported by Mike on Chris Dawson's preview,
      // 2026-09-07.
      //
      // This is the THIRD control in this shell with the same shape — the operation
      // dropdown set a key Home never read, and `hasQuoter` read a set the grant had
      // been pruned out of. A handler that sets state whose effect a fallback
      // absorbs is invisible to every static check in this repo: nothing is
      // undefined, nothing throws, the build is clean. Only rendering it and
      // clicking finds it. See tools/check-portal-controls.js.
      //
      // Back therefore clears the OPERATION as well, which is the state that was
      // actually pinning the view — that returns the reader to the operation list,
      // which is where "back" means something.
      const backTarget = portalOps.length > 1
        ? () => { setSelectedId(null); setSelectedOpKey(null); }
        : null;   // one operation: there is nowhere to go back TO, so offer nothing
      content = shownFarmer
        ? <FarmerView farmer={shownFarmer} prices={activePrices} priceMapForYear={_pricesFor} section={[...lobSet][0] || "spring"} onUpdate={updateFarmer} onBack={backTarget} onReset={resetFarmer} isMobile={isMobile} lobFilter={lobSet} customerView={!PORTAL_STAFF_ROLES.has(role)} onOpenMarket={(sec)=>{ setSelectedId(null); setMarketFocus(sec); setNavPersist(sec === "prf" ? "prfgrid" : "markets"); }} />
        : <div style={{ maxWidth: 760, margin: "0 auto" }}>
            <h2 style={{ color:"#1a2e1a", fontFamily:"'Lora',serif", fontSize: isMobile?21:25, margin:"0 0 14px" }}>My Coverage</h2>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {uniqueFarms.map(f => (
                <button key={f.id} onClick={()=>portalSelect(f.id)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", borderRadius:12, border:"1px solid #dde7dd", background:"#fff", cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
                  <span style={{ fontWeight:800, color:"#1a2e1a", fontSize:15 }}>{f.name}</span>
                  <span style={{ color:"#2d6a2d", fontWeight:700 }}>Open →</span>
                </button>
              ))}
            </div>
          </div>;
    } else if (cur === "quoters") {
      // Filtered by the WIDENED set, not `lobSet`. Putting Quoters on the nav off
      // the grant and then filtering its tabs by what the farm CARRIES opens an
      // empty hub reading "No quoters available for your coverage" — the nav and
      // the hub have to answer the same question, which is why marketHubTabs is
      // shared between MarketsHub and the nav.
      content = <QuotersHub farmers={opScopedFarmers} isMobile={isMobile} mode="portal" lobFilter={quoterLobSet} />;
    } else if (cur === "agcalc") {
      content = <AgCalcHub isMobile={isMobile} mode="portal" />;
    } else if (cur === "rainwatch") {
      content = <WeatherHub farmers={opScopedFarmers} isMobile={isMobile} mode="portal" preview={isPreview} />;
    } else if (cur === "markets") {
      // marketLobSet, NOT lobSet — see the note where it's built. Markets is the
      // one hub that shows a LOB the customer doesn't carry.
      content = <MarketsHub farmers={portalFarmers} isMobile={isMobile} mode="portal" lobFilter={marketLobSet} initialLob={marketFocus} tabFilter={marketTabSet} />;
    } else if (cur === "prfgrid") {
      // portalFarmers, so the book overlay IS this customer's own PRF and
      // nothing else. mode="portal" additionally strips the AGENCY chrome inside
      // the view — the "Our acres" map metric, the Internal/Public meeting
      // toggle, the policy-count column — and puts the rest in the customer's
      // voice. See the portal block at the top of PRFGridMapView.
      content = <PRFGridPage farmers={opScopedFarmers} isMobile={isMobile} mode="portal" />;
    } else if (cur === "prfreview") {
      // LEGACY ROUTE. Folded into My Coverage → PRF on 2026-09-07; kept reachable
      // because 45 live accounts carry page_access lists that name it and a saved
      // link should not strand a customer. Renders the same view the tab does.
      // Same gate as PRF Grid: the PRF grant, narrowed by what the farm carries.
      // Every customer table this view reads is farmer-scoped by RLS and is read
      // with the viewer own session, so the portal sees only their own rows.
      content = <PRFReviewView farmers={opScopedFarmers} isMobile={isMobile} mode="portal" />;
    } else if (cur === "claims") {
      // Limited agents (Daniele, Mock) get the Claim Manager board scoped to their granted
      // customers; farmer-role users get only the read-only Active/Paid claim views.
      content = <ClaimsTrackingView farmers={opScopedFarmers} isMobile={isMobile} onSelectFarmer={()=>{}} mode="portal" claimManager={role === "agent"} restrictFarmerIds={restrictIds || opScopedIds} />;
    } else if (cur === "fields") {
      // View-only field map, scoped to the farmer's own fields by RLS on
      // policy_fields / field_geometries (per-farmer via farmer_portal_users).
      // mode="portal" hides exports + agent card navigation. No onSelect passed.
      // portalFarmers/restrictIds are grant-scoped for a 578 holder too — that flag
      // means book-wide FILES, never book-wide fields.
      //
      // portalGrantIds, NOT restrictIds — the same reason Yield Analytics below
      // takes it. restrictIds is undefined for a real portal login (RLS already
      // scopes the row reads, so the client doesn't need to), but this screen's
      // Machine Data tab calls service-role John Deere handlers where RLS does
      // nothing and the request must NAME the farms. Passing restrictIds meant the
      // panel got no ids and told Panek "No farm is linked to your login yet" —
      // while the header above it counted their 2 farms and 342 fields. It worked
      // in preview only because an admin token forces needsClientScope true, which
      // is precisely the trap CLAUDE.md warns about: preview is not the portal.
      content = <FieldDataView farmers={opScopedFarmers} isMobile={isMobile} mode="portal" restrictFarmerIds={restrictIds || opScopedIds} />;
    } else if (cur === "yieldanalytics") {
      // portalGrantIds, not restrictIds: restrictIds is undefined for a real
      // farmer (RLS already scoped `farmers`), but these endpoints are read with
      // the SERVICE ROLE, so the request has to name the farms itself.
      content = <YieldAnalyticsView farmers={opScopedFarmers} isMobile={isMobile} mode="portal" restrictFarmerIds={opScopedIds} />;
    } else if (cur === "fieldprogress") {
      content = <HarvestTrackerView farmers={opScopedFarmers} isMobile={isMobile} mode="portal" restrictFarmerIds={opScopedIds} />;
    } else if (cur === "alerts") {
      content = <PortalAlertsView farmers={portalFarmers} isMobile={isMobile} />;
    } else if (cur === "fsa578") {
      // 578 viewer. Book-wide only for the explicit permission (bookFiles); every
      // other holder sees their own customers (per-farmer RLS), and an agent can
      // upload for them (canUpload — extract + farmer_578s INSERT are scoped to
      // their grants). `viewerAll` opens Pipeline and Library, which read the
      // agency Dropbox through endpoints that now refuse anyone without the
      // explicit permission, so the tab and the endpoint agree.
      content = <FSA578Screen isMobile={isMobile} farmers={fileFarmers} mode="portal" can426={canView426} canUpload={role === "agent"} canManage={role === "underwriter"} viewerAll={bookFiles} restrictFarmerIds={bookFiles ? undefined : restrictIds} />;
    } else if (cur === "market") {
      content = <MarketIntelligenceView isMobile={isMobile} canViewElevator={canViewElevator} mode="portal" />;
    } else {
      content = <div style={{ padding:40, color:"#677167" }}>Nothing to show yet. Contact your agent if you expected to see coverage here.</div>;
    }

    return (
      // Mobile mirrors the agent shell: a 100dvh flex-column app frame (slim
      // header, scrolling middle, in-flow bottom nav) so the nav stays glued to
      // the visible bottom on iOS Safari instead of a position:fixed top bar
      // that floats as the URL bar resizes the viewport. Desktop keeps the
      // sidebar row. The preview banner is a flex child above both.
      <div style={{
        display:"flex", flexDirection:"column",
        height: isMobile ? "100dvh" : undefined,
        minHeight: isMobile ? undefined : "100vh",
        overflow: isMobile ? "hidden" : undefined,
        fontFamily:"'Plus Jakarta Sans','Helvetica Neue',sans-serif", background:"#f2f6f2",
        ...(isPreview ? { border:"5px solid #b8541f", boxSizing:"border-box" } : {}),
      }}>
        {isPreview && (
          <div style={{ flexShrink:0, background:"#b8541f", color:"#fff", padding:"9px 16px", display:"flex", alignItems:"center", gap:12, fontWeight:700, fontSize:13, zIndex:9999, flexWrap:"wrap" }}>
            {/* This used to read "Read-only; your changes aren't affected." That
                was true when preview was the admin's own token being filtered in
                JS. It is NOT true now: preview runs the FARMER's real session, so
                anything saved here is written as them, to their data, and is
                indistinguishable afterwards from something they did themselves.
                The only record that it was you is portal_preview_audit. */}
            <span>👁 PREVIEW — you are signed in as <b>{displayName || "this user"}</b>{role ? ` (${role === "agent" ? "limited agent" : role})` : ""}. This is their real session: what’s missing here is missing for them. <b>Changes you save are live on their account.</b></span>
            <button onClick={()=>{ setSelectedId(null); endPreviewSession(); }}
              style={{ marginLeft:"auto", background:"#fff", color:"#b8541f", border:"none", borderRadius:6, padding:"6px 16px", fontWeight:800, fontSize:12.5, cursor:"pointer" }}>✕ Exit preview</button>
          </div>
        )}
      <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", flex:1, minHeight:0, minWidth:0 }}>
        {!isMobile && (
          <div style={{ width:220, background:"#1a2e1a", display:"flex", flexDirection:"column", flexShrink:0 }}>
            <div style={{ padding:"16px 16px 14px", borderBottom:"1px solid #2d4a2d" }}>
              <div style={{ display:"flex", alignItems:"center", gap:11, justifyContent:"center" }}>
              <img src="/clover-mark.png" alt="Clover Ag" style={{ width:44, height:44, flexShrink:0 }} />
              <div style={{ minWidth:0 }}>
                <img src="/silas-wordmark-light.svg" alt="SILAS" style={{ width:113, height:30, display:"block" }} />
                <div style={{ fontSize:7.5, fontWeight:600, letterSpacing:"0.05em", color:"#7aaa7a", textTransform:"uppercase", marginTop:4, lineHeight:1.3 }}>by Clover Ag Risk Management</div>
              </div>
            </div>
            </div>
            <div style={{ padding:"16px 12px", flex:1 }}>
              {/* Group heading over the whole nav. It used to read "My Coverage",
                  the same words as the nav item directly beneath it, so the page
                  appeared to name itself twice. */}
              {/* ── OPERATION SWITCHER — multi-operation logins only ────────────
                  One person can legitimately hold several insured entities:
                  partnerships, corporations, a family operation beside their own.
                  The model is one login -> one or more authorized operations, and
                  the active one has to be obvious or every downstream number is
                  ambiguous. "All operations" is deliberately NOT offered here: it
                  would flow into farm-specific claims, documents, quotes and
                  production, which is the merge this whole change removes. Home is
                  the surface that shows every operation, and it always does. */}
              {portalOps.length > 1 && (
                <div style={{ marginBottom: 12, paddingLeft: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: NAV_HEADING, textTransform: "uppercase", marginBottom: 6 }}>Operation</div>
                  <select
                    value={selectedOpKey || ""}
                    onChange={(e) => chooseOperation(e.target.value)}
                    style={{ width: "100%", padding: "8px 9px", borderRadius: 8, border: "1px solid #2d4a2d",
                             background: "#12240f", color: "#e8f0e8", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, boxSizing: "border-box" }}>
                    <option value="">All operations (Home only)</option>
                    {portalOps.map(o => <option key={o.key} value={o.key}>{o.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:NAV_HEADING, textTransform:"uppercase", marginBottom:8, paddingLeft:4 }}>Menu</div>
              {portalNav.map(n => (
                <NavBtn key={n.key} icon={n.icon} label={n.label} active={cur===n.key}
                  onClick={()=>{ setNavPersist(n.key); setSelectedId(null); }} />
              ))}
            </div>
            <div style={{ padding:"12px 16px", borderTop:"1px solid #2d4a2d" }}>
              {displayName && <div style={{ fontSize:11, color:"#7aaa7a", marginBottom:8, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{displayName}</div>}
              <PortalChangePassword />
              <button onClick={logout} style={{ width:"100%", background:"#2d4a2d", border:"none", borderRadius:6, padding:"8px", color:"#a8d878", fontSize:12, fontWeight:700, cursor:"pointer" }}>Sign out</button>
            </div>
          </div>
        )}
        {/* MOBILE OPERATION SWITCHER. Greg found the original defect on an iPhone,
            so the active operation has to be visible and changeable at 390px
            without opening anything. Its own row rather than the crowded header
            strip, so a long entity name cannot clip the sign-out control. */}
        {isMobile && portalOps.length > 1 && (
          <div style={{ flexShrink:0, background:"#12240f", padding:"7px 14px", borderBottom:"1px solid #2d4a2d",
                        display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:10.5, fontWeight:800, letterSpacing:"0.1em", color:"#7aaa7a", textTransform:"uppercase", flexShrink:0 }}>Operation</span>
            <select
              value={selectedOpKey || ""}
              onChange={(e) => chooseOperation(e.target.value)}
              style={{ flex:1, minWidth:0, padding:"6px 8px", borderRadius:7, border:"1px solid #2d4a2d",
                       background:"#1a2e1a", color:"#e8f0e8", fontFamily:"inherit", fontSize:12.5, fontWeight:700 }}>
              <option value="">All operations (Home only)</option>
              {portalOps.map(o => <option key={o.key} value={o.key}>{o.name}</option>)}
            </select>
          </div>
        )}
        {isMobile && (
          <div style={{ flexShrink:0, zIndex:100, background:"#1a2e1a", padding:"10px 14px", borderBottom:"1px solid #2d4a2d", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
              <img src="/clover-mark.png" alt="Clover Ag" style={{ width:24, height:24, flexShrink:0 }} />
              <div style={{ minWidth:0 }}>
                <img src="/silas-wordmark-light.svg" alt="SILAS" style={{ width:53, height:14, display:"block" }} />
                {displayName && <div style={{ fontSize:10.5, color:"#7aaa7a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:130 }}>{displayName}</div>}
              </div>
            </div>
            <div style={{ display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
              {selectedFarmer && (
                <button onClick={()=>setSelectedId(null)} style={{ background:"#2d4a2d", border:"none", borderRadius:6, padding:"6px 12px", color:"#a8d878", fontSize:12, fontWeight:700, cursor:"pointer" }}>← Back</button>
              )}
              {/* With more than five pages the account controls move into the
                  More menu; with five or fewer there is no More menu, so they
                  stay here — never unreachable. Same threshold PortalBottomNav uses. */}
              {portalNav.length <= 5 && <PortalChangePassword inline />}
              {portalNav.length <= 5 && <button onClick={logout} style={{ background:"#2d4a2d", border:"none", borderRadius:6, padding:"6px 12px", color:"#a8d878", fontSize:12, fontWeight:700, cursor:"pointer" }}>Sign out</button>}
            </div>
          </div>
        )}
        <div style={{ flex:1, minHeight:0, padding: isMobile ? "16px 14px 20px" : "32px 36px", overflowY:"auto", WebkitOverflowScrolling:"touch", minWidth:0 }}>
          {!isPreview && <PortalInstallTracker />}
          {/* Usage telemetry — real farmer sessions only. Admin "Preview as"
              is excluded so agency clicks never pollute the adoption numbers. */}
          {!isPreview && <PortalUsageTracker screen={cur} />}
          {!isPreview && <PortalInstallBanner />}
          {content}
        </div>
        {isMobile && (
          <PortalBottomNav items={portalNav} cur={cur} onNav={(k)=>{ setNavPersist(k); setSelectedId(null); }}
            account={portalNav.length > 5 ? (<>
              {displayName && <div style={{ fontSize:12, color:"#a0bea0" }}>Signed in as {displayName}</div>}
              <PortalChangePassword />
              <button onClick={logout} style={{ width:"100%", background:"#2d4a2d", border:"none", borderRadius:8, padding:"10px", color:"#a8d878", fontSize:13, fontWeight:700, cursor:"pointer" }}>Sign out</button>
            </>) : null} />
        )}
      </div>
      </div>
    );
  }

  return (
    <div style={{
      display:"flex",
      // Mobile: a fixed-height flex-column app shell. The top header and bottom
      // nav are in-flow flex children and only the middle scrolls, so NOTHING
      // is position:fixed against iOS Safari's moving visual viewport — that
      // moving viewport (URL bar showing/hiding on scroll) is what stranded the
      // bottom nav mid-screen ("floating"). 100dvh resizes the shell with the
      // URL bar so the nav stays glued to the visible bottom. Desktop keeps the
      // side-by-side row layout with the sidebar.
      flexDirection: isMobile ? "column" : "row",
      height: isMobile ? "100dvh" : undefined,
      minHeight: isMobile ? undefined : "100vh",
      overflow: isMobile ? "hidden" : undefined,
      fontFamily:"'Plus Jakarta Sans','Helvetica Neue',sans-serif", background:"#f2f6f2"
    }}>
      {!isMobile && (
        <div style={{ width:220, background:"#1a2e1a", display:"flex", flexDirection:"column", flexShrink:0 }}>
          <div style={{ padding:"16px 16px 14px", borderBottom:"1px solid #2d4a2d" }}>
            <div style={{ display:"flex", alignItems:"center", gap:11, justifyContent:"center" }}>
              <img src="/clover-mark.png" alt="Clover Ag" style={{ width:44, height:44, flexShrink:0 }} />
              <div style={{ minWidth:0 }}>
                <img src="/silas-wordmark-light.svg" alt="SILAS" style={{ width:113, height:30, display:"block" }} />
                <div style={{ fontSize:7.5, fontWeight:600, letterSpacing:"0.05em", color:"#7aaa7a", textTransform:"uppercase", marginTop:4, lineHeight:1.3 }}>by Clover Ag Risk Management</div>
              </div>
            </div>
            {/* The visible way into the quick switcher — a shortcut nobody knows
                about is not a feature. */}
            <button onClick={() => setSwitcherOpen(true)} title="Jump to any customer or page (Ctrl+K or /)"
              style={{ marginTop:12, width:"100%", display:"flex", alignItems:"center", gap:8, background:"#24392a", border:"1px solid #2d4a2d",
                       borderRadius:8, padding:"7px 10px", cursor:"pointer", color:"#9cc49c", fontSize:12.5, fontFamily:"inherit" }}>
              <span>🔍</span><span style={{ flex:1, textAlign:"left", whiteSpace:"nowrap" }}>Search…</span>
              <span style={{ fontSize:10.5, color:"#6f9a6f", border:"1px solid #3b5a3b", borderRadius:4, padding:"0 5px" }}>Ctrl K</span>
            </button>
          </div>
          <div style={{ padding:"14px 12px", flex:1, overflowY:"auto", minHeight:0 }}>
            {NAV_MODEL.map((g, gi) => {
              const items = g.items.filter(i => !i.adminOnly || isCurrentUserAdmin());
              if (!items.length) return null;
              const btns = items.map(i => (
                <NavBtn key={i.key} icon={i.icon} label={i.label}
                  active={nav === i.key && !selectedId}
                  onClick={() => { setNavPersist(i.key); setSelectedId(null); }} />
              ));
              // Ungrouped items (Dashboard) render bare, above the first heading.
              if (!g.group) return <div key={`g${gi}`}>{btns}</div>;
              // The group holding the page you are on is always open, so a collapsed
              // default can never hide where you are.
              const open = navGroupIsOpen(navOpen, g.group) || items.some(i => i.key === nav);
              return (
                <NavGroup key={g.group} title={g.group} open={open} onToggle={() => toggleNavGroup(g.group)}>
                  {btns}
                </NavGroup>
              );
            })}
          </div>
          {/* ── THE COUNTER IS HIDDEN ON HOME, AND THAT IS NOT COSMETIC ──
              Home exists so the app can be opened with a customer or a prospect
              in the room. This footer prints the SIZE OF THE BOOK — 133 fall,
              250 spring, 9 DRP, 19 LRP — in the app chrome, which sits beside
              Home whatever Home itself renders. Found on the very first
              screenshot of the finished page (2026-08-22): the page was clean
              and the frame around it was not, which is the whole reason to look
              at a picture rather than trust "no JS errors, 5,630 chars".
              Every other screen already shows book data by design, so this only
              ever hides on Home. */}
          {nav !== "home" && (
          <div style={{ padding:"12px 16px", borderTop:"1px solid #2d4a2d" }}>
            {/* Spring prices removed from the nav — they live on the Spring Prices page */}
            <div>
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.1em", color:NAV_HEADING, textTransform:"uppercase", marginBottom:6 }}>Policies on File</div>
              {[
                ["fall",   "🌾", "Fall Crops",   farmers.filter(f=>f.approvedYields?.some(a=>["WHEAT","BARLEY","TRITICALE","RYE","WINTER WHEAT"].includes(a.crop))).length],
                ["spring", "🌱", "Spring Crops", farmers.filter(f=>f.ppEligibility?.length>0||f.approvedYields?.some(a=>["CORN","SOYBEANS","DRY BEANS"].includes(a.crop))).length],
                ["drp",    "🥛", "DRP",          farmers.filter(f=>f.drpPolicies?.length>0).length],
                ["lrp",    "🐄", "LRP",          farmers.filter(f=>f.lrpPolicies?.length>0).length],
              ].map(([key,icon,label,count])=>(
                <div key={key} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4, fontSize:11 }}>
                  <span>{icon}</span>
                  <span style={{ color:"#7aaa7a" }}>{label}</span>
                  <span style={{ marginLeft:"auto", fontVariantNumeric: "tabular-nums", color:"#c8d8c8", fontWeight:700 }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      )}

      {isMobile && (
        <div style={{ flexShrink:0, zIndex:100, background:"#1a2e1a", padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid #2d4a2d" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <img src="/clover-mark.png" alt="Clover Ag" style={{ width:26, height:26, flexShrink:0 }} />
            <div>
              <img src="/silas-wordmark-light.svg" alt="SILAS" style={{ width:57, height:15, display:"block" }} />
              <div style={{ fontSize:7.5, color:"#7aaa7a", letterSpacing:"0.06em", textTransform:"uppercase", marginTop:2 }}>by Clover Ag Risk Management</div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {/* The phone way into the quick switcher — there is no keyboard
                shortcut on a phone, so without this the switcher is desktop-only. */}
            <button onClick={() => setSwitcherOpen(true)} aria-label="Search customers and pages"
              style={{ background:"#2d4a2d", border:"none", borderRadius:6, padding:"6px 10px", color:"#a8d878", fontSize:14, cursor:"pointer", minHeight:34, minWidth:40 }}>🔍</button>
            {selectedFarmer && (
              <button onClick={()=>setSelectedId(null)} style={{ background:"#2d4a2d", border:"none", borderRadius:6, padding:"6px 12px", color:"#a8d878", fontSize:12, fontWeight:700, cursor:"pointer", minHeight:34 }}>← Back</button>
            )}
          </div>
        </div>
      )}

      {/* Rendered once, outside both shells, so the desktop sidebar button and
          the phone header button open the same overlay. */}
      {switcherOpen && (
        <QuickSwitcher farmers={farmers}
          pages={[...NAV_MODEL.flatMap(g => g.items)].filter(i => !i.adminOnly || isCurrentUserAdmin())}
          onOpenPage={(key) => { setSelectedId(null); setNavPersist(key); }}
          onOpenCustomer={(id, section) => { setNavPersist("book"); setBookSection(section || "spring"); setSelectedId(id); }}
          onClose={() => setSwitcherOpen(false)} />
      )}

      <div style={{ flex:1, minHeight:0, padding: isMobile ? "16px 14px 20px" : "32px 36px", overflowY:"auto", WebkitOverflowScrolling:"touch", minWidth:0 }}>
        {selectedFarmer ? (
          <FarmerView farmer={selectedFarmer} prices={activePrices} priceMapForYear={_pricesFor} section={detailSection} onUpdate={updateFarmer} onBack={()=>setSelectedId(null)} onReset={resetFarmer} isMobile={isMobile} onOpenFields={()=>openFields(selectedFarmer?.id)} onOpenMarket={(sec)=>{ setSelectedId(null); setMarketFocus(sec); setNavPersist(sec === "prf" ? "prfgrid" : "markets"); }} />
        ) : nav==="home" ? (
          // NOTE the props: isMobile, a display name, and a nav callback. NO
          // `farmers`, and none is to be added — Home is safe to open in front
          // of a customer because it structurally cannot render book data, not
          // because a flag is switched off. tools/check-home-safe.js asserts it.
          <>
            {/* ADMIN-ONLY, agent shell only. Collapsed by default and NOT
                persisted, so a list of growers and claim amounts is never left
                on screen in front of a customer — the same rule the Data health
                control on this page already follows. It is deliberately NOT part
                of SilasHomeView, whose no-farmer-data contract is asserted by
                tools/check-home-safe.js and which also renders in the portal. */}
            <LiveBidsHomeButton isMobile={isMobile} onOpen={() => { setGrainDeepLink({ topTab: 'livebids' }); setSelectedId(null); setNavPersist("grain"); }} />
            <ClaimWatchBoard isMobile={isMobile} onSelectFarmer={(id)=>setSelectedId(id)} />
            <SilasHomeView isMobile={isMobile} displayName={getCurrentUserEmail() || ""} onNav={(k)=>{ setSelectedId(null); setNavPersist(k); }} />
          </>
        ) : nav==="dashboard" ? (
          <DashboardScreen farmers={farmers} isMobile={isMobile} />
        ) : nav==="attention" ? (
          // Second gate, same as scorecard: a persisted nav value must not land a
          // non-admin here without the hidden nav item.
          isCurrentUserAdmin()
            ? <NeedsAttentionView isMobile={isMobile} farmers={farmers} isAdmin={isCurrentUserAdmin()}
                onNav={(k)=>{ setSelectedId(null); setNavPersist(k); }}
                env={{ T, uiCard, uiPageTitle, UiSpinner, sbFetch, adminApiFetch, apiJson, cropYear: CURRENT_CROP_YEAR }} />
            : <DashboardScreen farmers={farmers} isMobile={isMobile} />
        ) : nav==="book" ? (
          <BookView farmers={farmers} prices={activePrices} isMobile={isMobile}
            onSelect={(id, section) => { setBookSection(section || "spring"); setSelectedId(id); }} />
        ) : nav==="fall" || nav==="spring" ? (
          // Unlisted in the nav — Book replaced these — but still routed so a
          // persisted sessionStorage value or an old link doesn't dead-end.
          <PolicySectionList section={nav} farmers={farmers} prices={activePrices} onSelect={setSelectedId} isMobile={isMobile} />
        ) : nav==="cashtargets" ? (
          <GrainTargetsView farmers={farmers} isMobile={isMobile} />
        ) : nav==="prfgrid" ? (
          <PRFGridPage farmers={farmers} isMobile={isMobile} />
        ) : nav==="quoters" ? (
          <QuotersHub farmers={farmers} isMobile={isMobile} initialTab={quoterFocus} />
        ) : nav==="agcalc" ? (
          <AgCalcHub isMobile={isMobile} />
        ) : nav==="adjuster" ? (
          <AdjusterAskView isMobile={isMobile} />
        ) : nav==="markets" ? (
          // Admins see every market tab: no lob scope, no per-user allowlist.
          // The experimental Market Barometer is admin-only (Kevin, 2026-10-19): a
          // REAL admin session, never preview, never the portal shell.
          <MarketsHub farmers={farmers} isMobile={isMobile} initialLob={marketFocus} tabFilter={null}
            barometer={isRealAdminSession() && !getPreviewToken() ? <MarketBarometerSection isMobile={isMobile} /> : null} />
        ) : nav==="alerts" ? (
          <AlertsManagerView isMobile={isMobile} />
        ) : nav==="prices" ? (
          <PriceScreen pricesByYear={pricesByYear} onSave={saveYearPrices} dryBeanPrices={dryBeanPrices} onSaveDryBeans={saveDryBeanPrices} isMobile={isMobile} />
        ) : nav==="import" ? (
          <ImportScreen farmers={farmers} onImport={handleImport} isMobile={isMobile} />
        ) : nav==="synclog" ? (
          <SyncLogScreen isMobile={isMobile} />
        ) : nav==="map" ? (
          <MapView farmers={farmers} isMobile={isMobile} />
        ) : nav==="fields" ? (
          <FieldDataView farmers={farmers} prices={activePrices} onSelect={setSelectedId} isMobile={isMobile} initialFarmerId={fieldsFarmerId}
            initialFocus={fieldFocus} onFocusConsumed={() => setFieldFocus(null)} onNav={(k)=>{ setSelectedId(null); setNavPersist(k); }} />
        ) : nav==="rainwatch" ? (
          <WeatherHub farmers={farmers} isMobile={isMobile} onNav={(k)=>{ setSelectedId(null); setNavPersist(k); }} />
        ) : nav==="wxrecords" ? (
          // Weather Records is the Weather page's admin Claim report tab now
          // (Kevin, 2026-11-01). The route stays so a saved link or a persisted nav
          // lands on that tab; WeatherHub offers the tab to admins only.
          <WeatherHub farmers={farmers} isMobile={isMobile} initialTab="report"
            onNav={(k)=>{ setSelectedId(null); setNavPersist(k); }} />
        ) : nav==="harvesttracker" ? (
          <HarvestTrackerView isMobile={isMobile} farmers={farmers} />
        ) : nav==="fsa578" ? (
          <FSA578Screen isMobile={isMobile} farmers={farmers} />
        ) : nav==="grain" || nav==="grainaction" ? (
          // "grainaction" is retired as its own nav item (ROADMAP.md §1.2f
          // Milestone 2 — it's the Summary tab inside Grain Marketing now),
          // kept routed here so a persisted sessionStorage value or an old
          // deep link lands on the real thing instead of dead-ending, same
          // precedent as the unlisted fall/spring routes elsewhere in this
          // dispatch. An admin landing this way still gets the Summary tab —
          // GrainMarketingView switches to it itself once a farm is picked.
          <GrainMarketingView farmers={farmers} isMobile={isMobile} initialSelection={grainDeepLink} onConsumeInitialSelection={() => setGrainDeepLink(null)} />
        ) : nav==="market" ? (
          // No second password: this shell only renders for a signed-in admin
          // session (App routes everyone else to the portal), and the old MI
          // gate waved any signed-in session straight through. Cut 2026-11-02.
          <MarketIntelligenceView isMobile={isMobile} canViewElevator={true} />
        ) : nav==="cropreview" ? (
          <CropReviewView farmers={farmers} onSelect={setSelectedId} isMobile={isMobile} onOpenField={openFieldOnMap} />
        ) : nav==="yieldanalytics" ? (
          <YieldAnalyticsView farmers={farmers} isMobile={isMobile} />
        ) : nav==="farmprograms" ? (
          <FarmProgramsView farmers={farmers} isMobile={isMobile} />
        ) : nav==="coverage" || nav==="prfreview" ? (
          <CoverageAdvisorView farmers={farmers} isMobile={isMobile} initialView={nav==="prfreview" ? "prf" : null}
            onOpenFarmPrograms={(tab, fid) => { farmProgramsHandoff(tab, fid); setNavPersist("farmprograms"); }} />
        ) : nav==="historical" ? (
          <HistoricalDataScreen isMobile={isMobile} />
        ) : nav==="directory" ? (
          <MailingDirectoryView isMobile={isMobile} />
        ) : nav==="portaladmin" ? (
          <PortalAdminView isMobile={isMobile} onPreview={setPreviewAs} />
        ) : nav==="scorecard" ? (
          isCurrentUserAdmin() ? <YieldScorecardView farmers={farmers} isMobile={isMobile} /> : <DashboardScreen farmers={farmers} isMobile={isMobile} />
        ) : nav==="premium" ? (
          // Second gate: a persisted sessionStorage nav or an old deep link can
          // land a non-admin here without ever touching the (hidden) nav item.
          isCurrentUserAdmin() ? <PremiumAnalyticsView isMobile={isMobile} /> : <DashboardScreen farmers={farmers} isMobile={isMobile} />
        ) : nav==="claims" ? (
          <ClaimsTrackingView farmers={farmers} isMobile={isMobile} onSelectFarmer={(f, claim) => {
            // Open the unified farmer card OVER the Claims page. Deliberately do NOT switch
            // nav to a per-LOB section — the unified card replaced those pages, so setNav
            // ("spring") sent Back to a page that no longer exists. Keeping nav="claims"
            // means Back (setSelectedId(null)) returns to Claims, where they came from.
            // Resolve to the farmer that actually holds the claimed policy.
            const claimPN = claim?.policyNumber;
            let targetFarmer = f;
            if (claimPN) {
              const match = farmers.find(fm => fm.policyNumber === claimPN || fm._allPolicyNums?.includes(claimPN));
              if (match) targetFarmer = match;
            }
            setSelectedId(targetFarmer.policyId || targetFarmer.id);
          }} />
        ) : (
          // Guard: an unknown/retired nav key (persisted sessionStorage or an old
          // link — e.g. a since-removed per-LOB page) falls back to the dashboard
          // instead of a blank screen.
          <DashboardScreen farmers={farmers} isMobile={isMobile} />
        )}
      </div>

      {isMobile && (
        <BottomNav nav={nav} selectedId={selectedId} setNav={setNav} setSelectedId={setSelectedId} />
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// MAILING DIRECTORY — sortable, filterable farmer contact roster with Excel export
//
// A book-wide contact directory. State comes from `policies` (farmers.state is
// almost always null); County + Crop come from `crop_coverage` (the authoritative
// physical county — policies.county is the FSA admin county trap). All three are
// aggregated per farmer as sets, because one grower can span multiple states,
// counties, and crops. State/County/Crop filters are multi-select; the currently
// filtered rows export to .xlsx via SheetJS (loaded on demand, same CDN as the
// bulk-import path). Only ~14 farmers carry an email today, so the directory leads
// with physical address + phone and offers a "Has email" quick filter.
// ─────────────────────────────────────────────────────────────────────────────
const US_STATE_CODES = new Set("AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" "));
function MailingDirectoryView({ isMobile = false }) {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState(null);
  const [search, setSearch]       = useState("");
  const [selStates, setSelStates]     = useState(() => new Set());
  const [selCounties, setSelCounties] = useState(() => new Set());
  const [selCrops, setSelCrops]       = useState(() => new Set());
  const [emailOnly, setEmailOnly]     = useState(false);
  const [addrOnly, setAddrOnly]       = useState(false);
  const [sortKey, setSortKey]     = useState("name");
  const [sortDir, setSortDir]     = useState("asc");
  const [exporting, setExporting] = useState(false);
  const [showCounties, setShowCounties] = useState(false);

  const titleCase = (s) => (s || "").split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  // "35-NY" (RMA state code) → "NY"; leaves plain "NY" untouched.
  const normState = (s) => { const m = /^\d+-([A-Za-z]{2})$/.exec(String(s || "").trim()); return (m ? m[1] : String(s || "").trim()).toUpperCase(); };
  // Mailing addresses are stored as one combined string (the city/zip columns are empty).
  // Best-effort split into street / city / state / zip for mail merge; the raw string is
  // always kept authoritative. Handles comma-form ("4264 OAK HILL RD, SILVER SPRINGS, NY
  // 14550") and space-form ("341 FOX RD ARMAGH PA 15920-0000"); strips "-0000" tails.
  const parseAddr = (raw) => {
    let s = String(raw || "").trim();
    if (!s) return { street: "", city: "", state: "", zip: "" };
    let zip = "", state = "";
    const zm = /(\d{5})(?:-\d{4})?\s*$/.exec(s);
    if (zm) { zip = zm[1]; s = s.slice(0, zm.index).replace(/[,\s]+$/, ""); }
    const sm = /(?:^|[,\s])([A-Za-z]{2})\s*$/.exec(s);
    // Only a real state code is a state. Any trailing two letters used to qualify,
    // so an address ending "... SALT RD" with no state was filed under a state "RD".
    if (sm && US_STATE_CODES.has(sm[1].toUpperCase())) { state = sm[1].toUpperCase(); s = s.slice(0, sm.index).replace(/[,\s]+$/, ""); }
    let street = s, city = "";
    if (s.includes(",")) { const parts = s.split(",").map(p => p.trim()).filter(Boolean); city = parts.pop() || ""; street = parts.join(", "); }
    else { const toks = s.split(/\s+/); if (toks.length > 1) { city = toks.pop(); street = toks.join(" "); } }
    if (/^\d+$/.test(city)) { street = s; city = ""; }   // e.g. "PO BOX 73" — trailing token is a number, not a city
    return { street, city, state, zip };
  };

  // Names are stored as one string, so a plain A–Z sort orders by FIRST name.
  // Derive a surname key for phone-book ordering. entity_type is null on 279/283
  // rows, so business-vs-person is inferred from the name itself:
  //   "BRADLEY T WILSON"                        → WILSON        (person)
  //   "BENTLEY, ERIC D (OAK HILL FARMS)"        → BENTLEY       (already inverted)
  //   "ARTHUR B DOUGLAS J BENEDICT (ARBOR FARMS)" → BENEDICT    (person + DBA)
  //   "BOXER RIDGE FARMS LLC"                   → the full name (business: no surname)
  const ENTITY_TOKENS = /\b(LLC|L\.L\.C|INC|INCORPORATED|CORP|CORPORATION|LP|LLP|LTD|COMPANY|PARTNERSHIP|PARTNERS|TRUST|ENTERPRISES|FARM|FARMS|FARMING|DAIRY|ACRES|AGRICULTURE|ORCHARD|ORCHARDS|VINEYARD|VINEYARDS|GRAIN|PRODUCE|RANCH)\b/;
  const NAME_SUFFIX   = /^(JR|SR|II|III|IV|V)$/;
  const parseName = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return { lastName: "", isEntity: false, inverted: "" };
    const pm = /\s*\(([^)]*)\)\s*$/.exec(s);              // trailing DBA, e.g. "(ARBOR FARMS)"
    const base = (pm ? s.slice(0, pm.index) : s).trim();
    const dba = pm ? ` (${pm[1]})` : "";
    if (base.includes(",")) {                             // already "LAST, FIRST"
      return { lastName: base.split(",")[0].trim().toUpperCase(), isEntity: false, inverted: s };
    }
    if (ENTITY_TOKENS.test(base.toUpperCase())) {
      return { lastName: base.toUpperCase(), isEntity: true, inverted: s };
    }
    const toks = base.split(/\s+/).filter(Boolean);
    const suffix = [];
    while (toks.length > 1 && NAME_SUFFIX.test(toks[toks.length - 1].toUpperCase().replace(/\./g, ""))) suffix.unshift(toks.pop());
    if (toks.length < 2) return { lastName: base.toUpperCase(), isEntity: false, inverted: s };
    const last = toks.pop();
    const inverted = `${last}, ${toks.join(" ")}${suffix.length ? " " + suffix.join(" ") : ""}${dba}`;
    return { lastName: last.toUpperCase(), isEntity: false, inverted };
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [farmers, policies, coverage] = await Promise.all([
          sbFetch("farmers", { select: "id,name,entity_type,customer_type,address,city,state,zip,phone,email" }).catch(() => []),
          sbFetch("policies", { select: "id,farmer_id,state,county,policy_type" }).catch(() => []),
          sbFetch("crop_coverage", { select: "policy_id,crop,county" }).catch(() => []),
        ]);
        const polById = new Map();
        policies.forEach(p => polById.set(p.id, p));
        const agg = new Map();
        const ensure = (fid) => {
          if (!agg.has(fid)) agg.set(fid, { states: new Set(), counties: new Set(), crops: new Set(), lobs: new Set() });
          return agg.get(fid);
        };
        policies.forEach(p => {
          if (!p.farmer_id) return;
          const a = ensure(p.farmer_id);
          if (p.state) a.states.add(normState(p.state));
          if (p.policy_type) a.lobs.add(p.policy_type);
          // policies.county is the FSA admin county — used only as a location fallback
          // for non-crop farmers (DRP/LRP) who have no crop_coverage county.
        });
        coverage.forEach(c => {
          const pol = polById.get(c.policy_id);
          if (!pol || !pol.farmer_id) return;
          const a = ensure(pol.farmer_id);
          if (c.crop) a.crops.add(String(c.crop).trim().toUpperCase());
          if (c.county) a.counties.add(String(c.county).trim().toUpperCase());
        });
        // Fold in policies.county only where crop_coverage gave a farmer no county at all.
        policies.forEach(p => {
          if (!p.farmer_id || !p.county) return;
          const a = agg.get(p.farmer_id);
          if (a && a.counties.size === 0) a.counties.add(String(p.county).trim().toUpperCase());
        });
        // Skip diagnostic/test rows (name starts with "__diag_") — same convention the
        // main Dashboard uses. These are import write-test artifacts, not real farmers.
        const out = farmers.filter(f => !f.name?.startsWith("__diag_")).map(f => {
          const a = agg.get(f.id) || { states: new Set(), counties: new Set(), crops: new Set(), lobs: new Set() };
          const addr = f.address || "";
          const p = parseAddr(addr);
          const nm = parseName(f.name || "");
          return {
            id: f.id,
            name: f.name || "",
            lastName: nm.lastName,      // surname (people) / business name (entities)
            isEntity: nm.isEntity,
            invertedName: nm.inverted,  // "WILSON, BRADLEY T" — shown when sorting by last name
            entityType: f.entity_type || "",
            customerType: f.customer_type || "",
            address: addr,                                   // full combined mailing string (authoritative)
            street: p.street,
            // stored city/zip columns are essentially empty — fall back to the parsed values
            city: (f.city && f.city.trim()) || p.city,
            zip: (f.zip && f.zip.trim()) || p.zip,
            // Mailing state = the state on the actual mailing address (one destination).
            // Fall back to the insurance policy state only when there's no address parsed.
            // This is deliberately NOT the union of policy states — a farmer mailed in PA
            // whose one policy is mis-tagged NY should show PA, not "NY, PA".
            state: p.state || (a.states.size ? [...a.states].sort()[0] : ""),
            phone: f.phone || "",
            email: f.email || "",
            states: [...a.states].sort(),
            counties: [...a.counties].sort(),
            crops: [...a.crops].sort(),
            lobs: [...a.lobs].sort(),
          };
        });
        if (alive) { setRows(out); setLoading(false); }
      } catch (e) {
        if (alive) { setErr(e?.message || String(e)); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, []);

  const allStates   = useMemo(() => [...new Set(rows.map(r => r.state).filter(Boolean))].sort(), [rows]);
  const allCounties = useMemo(() => [...new Set(rows.flatMap(r => r.counties))].sort(), [rows]);
  const allCrops    = useMemo(() => [...new Set(rows.flatMap(r => r.crops))].sort(), [rows]);

  const mkToggle = (setFn) => (val) => setFn(prev => { const n = new Set(prev); if (n.has(val)) n.delete(val); else n.add(val); return n; });
  const toggleState  = mkToggle(setSelStates);
  const toggleCounty = mkToggle(setSelCounties);
  const toggleCrop   = mkToggle(setSelCrops);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter(r => {
      if (q && !(`${r.name} ${r.city} ${r.email} ${r.phone} ${r.address}`.toLowerCase().includes(q))) return false;
      if (emailOnly && !r.email) return false;
      if (addrOnly && !r.address) return false;
      if (selStates.size   && !selStates.has(r.state)) return false;
      if (selCounties.size && !r.counties.some(c => selCounties.has(c))) return false;
      if (selCrops.size    && !r.crops.some(c => selCrops.has(c))) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const keyval = (r) => {
      switch (sortKey) {
        // Phone-book order: surname first, full name as the tiebreak within a surname.
        case "lastname": return `${r.lastName}|${r.name}`.toLowerCase();
        case "city":   return r.city.toLowerCase();
        case "state":  return r.state || "";
        case "county": return r.counties[0] || "";
        case "crops":  return r.crops.join(",");
        case "email":  return r.email.toLowerCase();
        default:       return r.name.toLowerCase();
      }
    };
    return [...list].sort((a, b) => { const av = keyval(a), bv = keyval(b); return av < bv ? -dir : av > bv ? dir : 0; });
  }, [rows, search, emailOnly, addrOnly, selStates, selCounties, selCrops, sortKey, sortDir]);

  const setSort = (k) => { if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };
  const clearAll = () => { setSelStates(new Set()); setSelCounties(new Set()); setSelCrops(new Set()); setSearch(""); setEmailOnly(false); setAddrOnly(false); };
  const activeFilters = selStates.size + selCounties.size + selCrops.size + (search.trim() ? 1 : 0) + (emailOnly ? 1 : 0) + (addrOnly ? 1 : 0);

  const exportExcel = async () => {
    if (!filtered.length) return;
    setExporting(true);
    try {
      if (!window.XLSX) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js";
          s.onload = res; s.onerror = rej; document.head.appendChild(s);
        });
      }
      const data = filtered.map(r => ({
        Name: r.name,
        // Surname for people; blank for businesses (they have no last name to sort on).
        "Last Name": r.isEntity ? "" : r.lastName,
        "Entity Type": r.entityType,
        "Mailing Address": r.address,
        Street: r.street,
        City: r.city,
        "Mail State": r.state,
        Zip: r.zip,
        "Ins. State": r.states.join(", "),
        County: r.counties.map(titleCase).join(", "),
        Crops: r.crops.map(titleCase).join(", "),
        Phone: r.phone,
        Email: r.email,
      }));
      const ws = window.XLSX.utils.json_to_sheet(data);
      ws["!cols"] = [{ wch: 28 }, { wch: 15 }, { wch: 13 }, { wch: 34 }, { wch: 22 }, { wch: 16 }, { wch: 9 }, { wch: 8 }, { wch: 9 }, { wch: 20 }, { wch: 22 }, { wch: 15 }, { wch: 26 }];
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, "Mailing Directory");
      const stamp = new Date().toISOString().slice(0, 10);
      window.XLSX.writeFile(wb, `clover-ag-mailing-directory_${stamp}.xlsx`);
    } catch (e) {
      alert("Excel export failed: " + (e?.message || e));
    } finally {
      setExporting(false);
    }
  };

  const chip = (label, active, onClick) => (
    <button key={label} onClick={onClick} style={{
      padding: "4px 10px", borderRadius: 20, border: active ? "2px solid #2d4a2d" : "1px solid #d0e0d0",
      background: active ? "#2d4a2d" : "#fff", color: active ? "#fff" : "#4a6e4a",
      fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    }}>{label}</button>
  );

  const th = (label, key, align = "left") => (
    <th onClick={key ? () => setSort(key) : undefined} style={{
      textAlign: align, padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#4a6e4a",
      textTransform: "uppercase", letterSpacing: "0.04em", cursor: key ? "pointer" : "default",
      borderBottom: "2px solid #e4ece4", whiteSpace: "nowrap", background: "#f2f6f2",
      position: "sticky", top: 0, zIndex: 1, userSelect: "none",
    }}>{label}{key && sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}</th>
  );

  const filterGroup = (title, options, sel, toggleFn, opts = {}) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>
        {title}{sel.size > 0 && <span style={{ color: "#2d4a2d" }}> · {sel.size}</span>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: opts.scroll ? 96 : "none", overflowY: opts.scroll ? "auto" : "visible" }}>
        {options.length === 0
          ? <span style={{ fontSize: 12, color: "#677167" }}>—</span>
          : options.map(o => chip(opts.fmt ? opts.fmt(o) : o, sel.has(o), () => toggleFn(o)))}
      </div>
    </div>
  );

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}>Loading directory…</div>;
  if (err) return <div style={{ padding: 20, color: "#c62828", fontSize: 13 }}>Failed to load directory: {err}</div>;

  const emailCount = rows.filter(r => r.email).length;
  const addrCount  = rows.filter(r => r.address).length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
        <h2 style={{ ...uiPageTitle(isMobile) }}>Mailing Directory</h2>
        <button onClick={exportExcel} disabled={exporting || !filtered.length} style={{
          padding: "8px 16px", borderRadius: 8, border: "1px solid #2d4a2d",
          background: filtered.length ? "#2d4a2d" : "#c8d8c8", color: "#fff",
          fontSize: 13, fontWeight: 700, cursor: filtered.length ? "pointer" : "default", display: "flex", alignItems: "center", gap: 6,
        }}>📥 {exporting ? "Exporting…" : `Export Excel (${filtered.length})`}</button>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 12, color: "#60735f" }}>
        {rows.length} contacts · {addrCount} have a mailing address, {emailCount} have email · County &amp; Crops from coverage
        {activeFilters > 0 && <span style={{ color: "#2d4a2d", fontWeight: 600 }}> · showing {filtered.length}</span>}
      </p>

      {/* ── Filters ── */}
      <div style={{ background: "#fff", border: "1px solid #e4ece4", borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, city, email, phone…"
            style={{ flex: "1 1 240px", padding: "8px 12px", borderRadius: 8, border: "1px solid #cdd9cd", fontSize: 13, color: "#1a2e1a", background: "#fff" }} />
          {chip("✉ Has email", emailOnly, () => setEmailOnly(v => !v))}
          {chip("🏠 Has address", addrOnly, () => setAddrOnly(v => !v))}
          {chip("🔤 Sort by last name", sortKey === "lastname", () => setSort("lastname"))}
          {activeFilters > 0 && (
            <button onClick={clearAll} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #d0c0c0", background: "#fff", color: "#a05050", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Clear ({activeFilters})</button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "auto 1fr", gap: isMobile ? 8 : 24 }}>
          <div>
            {filterGroup("State", allStates, selStates, toggleState)}
            {filterGroup("Crop", allCrops, selCrops, toggleCrop, { fmt: titleCase })}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5, display: "flex", alignItems: "center", gap: 8 }}>
              County{selCounties.size > 0 && <span style={{ color: "#2d4a2d" }}>· {selCounties.size}</span>}
              <button onClick={() => setShowCounties(v => !v)} style={{ marginLeft: "auto", background: "none", border: "none", color: "#4a6e4a", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {showCounties ? "Hide" : `Show all ${allCounties.length}`}
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: showCounties ? "none" : 68, overflowY: showCounties ? "visible" : "auto" }}>
              {allCounties.length === 0
                ? <span style={{ fontSize: 12, color: "#677167" }}>—</span>
                : allCounties.map(o => chip(titleCase(o), selCounties.has(o), () => toggleCounty(o)))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      {/* A PHONE GETS ONE CARD PER CONTACT. The 780px table showed only Name and
          Address on a 390px screen, with the phone number — the thing you reach for
          the directory for on a phone — a sideways swipe away. Same rows, same order,
          same filters; the number dials and the email opens a message. */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#677167", fontSize: 13, background: "#fff", border: "1px solid #e4ece4", borderRadius: 10 }}>No contacts match these filters.</div>
          ) : filtered.map((r) => (
            <div key={r.id} style={{ background: "#fff", border: "1px solid #e4ece4", borderRadius: 12, padding: "11px 13px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a" }}>
                {sortKey === "lastname" ? r.invertedName : r.name}
                {r.entityType && <span style={{ marginLeft: 6, fontSize: 10, color: "#60735f", fontWeight: 500 }}>{r.entityType}</span>}
              </div>
              <div style={{ fontSize: 12.5, color: "#3a5a3a", marginTop: 3 }}>{r.address || <span style={{ color: "#a8b8a8" }}>No address on file</span>}</div>
              {(r.counties.length > 0 || r.crops.length > 0) && (
                <div style={{ fontSize: 11.5, color: "#60735f", marginTop: 3 }}>
                  {[r.counties.map(titleCase).join(", "), r.crops.map(titleCase).join(", ")].filter(Boolean).join(" · ")}
                </div>
              )}
              {(r.phone || r.email) && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                  {r.phone && <a href={"tel:" + String(r.phone).replace(/[^\d+]/g, "")}
                    style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", textDecoration: "none", border: "1px solid #cdd9cd", borderRadius: 999, padding: "5px 12px" }}>📞 {r.phone}</a>}
                  {r.email && <a href={"mailto:" + r.email}
                    style={{ fontSize: 12.5, fontWeight: 600, color: "#2d4a2d", textDecoration: "none", border: "1px solid #cdd9cd", borderRadius: 999, padding: "5px 12px", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>✉ {r.email}</a>}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
      <div style={{ overflowX: "auto", border: "1px solid #e4ece4", borderRadius: 10, background: "#fff", maxHeight: "68vh", overflowY: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 780 }}>
          <thead>
            <tr>
              {th(sortKey === "lastname" ? "Last Name" : "Name", sortKey === "lastname" ? "lastname" : "name")}
              {th("Mailing Address", "city")}
              {th("State", "state")}
              {th("County", "county")}
              {th("Crops", "crops")}
              {th("Phone", null)}
              {th("Email", "email")}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#677167", fontSize: 13 }}>No contacts match these filters.</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={r.id} style={{ background: i % 2 ? "#fafcfa" : "#fff" }}>
                <td style={{ padding: "8px 10px", fontSize: 13, fontWeight: 600, color: "#1a2e1a", borderBottom: "1px solid #f0f4f0" }}>
                  {/* In last-name sort, people render inverted ("WILSON, BRADLEY T") so the
                      ordering is self-evident; businesses have no surname and stay as-is. */}
                  {sortKey === "lastname" ? r.invertedName : r.name}
                  {r.entityType && <span style={{ marginLeft: 6, fontSize: 10, color: "#60735f", fontWeight: 500 }}>{r.entityType}</span>}
                </td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "#3a5a3a", borderBottom: "1px solid #f0f4f0", minWidth: 220 }}>{r.address || <span style={{ color: "#c8d8c8" }}>— no address on file</span>}</td>
                <td style={{ padding: "8px 10px", fontSize: 13, color: "#3a5a3a", borderBottom: "1px solid #f0f4f0", whiteSpace: "nowrap" }}>{r.state || <span style={{ color: "#c8d8c8" }}>—</span>}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "#3a5a3a", borderBottom: "1px solid #f0f4f0" }}>{r.counties.length ? r.counties.map(titleCase).join(", ") : <span style={{ color: "#c8d8c8" }}>—</span>}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "#3a5a3a", borderBottom: "1px solid #f0f4f0" }}>{r.crops.length ? r.crops.map(titleCase).join(", ") : <span style={{ color: "#c8d8c8" }}>—</span>}</td>
                <td style={{ padding: "8px 10px", fontSize: 13, color: "#3a5a3a", borderBottom: "1px solid #f0f4f0", whiteSpace: "nowrap" }}>{r.phone || <span style={{ color: "#c8d8c8" }}>—</span>}</td>
                <td style={{ padding: "8px 10px", fontSize: 13, color: r.email ? "#2d4a2d" : "#c8d8c8", borderBottom: "1px solid #f0f4f0", whiteSpace: "nowrap" }}>{r.email || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// CROP REVIEW — Year-over-year acres / yield / guarantee QC, with sign-off
//
// Compares incoming CURRENT_CROP_YEAR coverage (as it gets keyed into the AIPs and
// loaded) against last year's planted acres + APH yield, by crop & county, so Kevin
// and Mike can catch transpositions, dropped crops, and big swings before they
// matter. Each farm gets a one-click sign-off (either reviewer); signed farms move
// to the Reviewed tab. Works for both Spring and Fall crops.
//
// SOURCES (verified 2026-06-19):
//  - THIS YEAR: crop_coverage joined to policies (crop_year=CURRENT). The BASE plan
//    row carries acres + aph_yield (SCO/ECO/ARPI add-on rows have null acres and
//    only contribute liability layers). Bushel guarantee/ac = aph_yield × cov_level.
//  - LAST YEAR (acres): production_history (the APH database — the only table that
//    retains prior-year reported acres). Dedup: each (farmer,crop,county) can carry
//    a typed M13 sync row AND a null-record_type Excel-import mirror with identical
//    acres — prefer the typed M13 rows; fall back to untyped only when no M13 row.
//  - LAST YEAR (APH): coverage_snapshots (crop_year=PRIOR) — the year-tagged copy of
//    last year's crop_coverage, recovered from the prior-year M13/NAU file via the
//    sync backfill `?snapshots=1` path. aph_yield here is the RMA APPROVED APH (10-yr
//    avg, P15 f[36]), acre-weighted across base rows exactly like the CURRENT column.
//    This makes the yield comparison true APH-vs-APH. (Earlier this column used the
//    single-season reported yield from production_history — a drought year then read
//    as a scary "drop" that wasn't an APH change at all.) Last-year liability/guarantee
//    aren't shown yet (snapshots only seeded 2026-06-19 onward), so CURRENT liability/
//    guarantee show as context until two full snapshot years exist.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// YIELD ANALYTICS — which acres are good, and which are dependable. Per crop.
//
// Field-level history for the John Deere–connected farms: 2,807 field-crop-years
// across 848 fields, 2015-2026. 388 fields carry 3+ years, 215 carry 5+.
//
// ONE CROP AT A TIME. This page used to answer everything across whatever a field
// had grown — one CV, one performance number, one label per field, blended over
// corn and soybeans and wheat. That is not a question anyone asks. Corn ground and
// wheat ground are different assets on the same acres, and a field can be your
// best corn and your worst wheat. So the crop is now the axis: pick CORN and every
// number on the page is corn, computed only from corn years.
//
// WITHIN A CROP — index, never raw bushels.
// 2019 corn and 2024 corn are not the same season. On this book the season alone
// moves a farm's whole corn average by CV 0.14 — the same size as the entire
// field-level signal — so raw bushels would mostly measure the weather. Every
// harvest is therefore divided by what the REST of that farm did with THAT crop
// THAT year:
//
//     yield_index = field bu/ac  ÷  operation bu/ac (same crop, same year)
//
// 1.20 means "20% better than the rest of this farm's corn ground that year",
// whatever the season. What's left after that division is the ground.
//
//   PERFORMANCE = mean(index)  over that crop's years  — how good the ground is
//   STABILITY   = CV of those indexes                  — how dependable it is
//   RANK        = standing among that farm's fields of the same crop, on the
//                 multi-year index — so it's a record, not last season's table
//
// Performance and stability are independent, and crossing them is the whole point:
// two fields averaging 1.23 are NOT the same asset if one has CV 0.03 and the
// other 0.95.
//
//   QUADRANTS, now struck per crop
//     Above · Stable    → your best ground for this crop
//     Above · Unstable  → real upside, and where insurance earns out
//     Below · Stable    → consistently poor; a good year will NOT save it
//     Below · Unstable  → usually drainage or drought; diagnosable
//
// NO ECONOMICS, on purpose (removed 2026-08-04). $/ac applied ONE price and ONE
// cost per crop to every field, so every dollar gap inside a farm was just the
// yield gap wearing a dollar sign — rent, drying and haul distance are the real
// per-field costs and we don't hold them. TREND went too: an OLS slope over 3-8
// points is a line through noise, and it invited "this field is declining"
// conclusions the sample can't carry.
//
// GUARDS, learned from the first run against real data: tiny fields and thin
// crop-years produce wild indices (a 7.2-acre corner posted CV 0.95). A field-year
// is only indexed when the operation has MIN_PEERS fields of that crop that year,
// fields below MIN_ACRES are excluded, and a crop block needs MIN_CROP_YEARS of
// that crop to show at all / MIN_CV_YEARS before a CV is claimed.
// ─────────────────────────────────────────────────────────────────────────────
const YA_MIN_YEARS  = 3;    // harvest seasons on the field, any crop
const YA_MIN_ACRES  = 5;    // small corners swing the index wildly
const YA_MIN_PEERS  = 3;    // fields of that crop that year, else no honest op mean
const YA_MIN_CROP_YEARS = 2; // years of THAT crop before the field is scored in it
const YA_MIN_CV_YEARS   = 3; // ...and before a CV is a statistic rather than a gap
// THE STABLE / UNSTABLE CUT IS YA_HIGH_CV, and it must equal api/_jd.js's YA_HIGH_CV
// (the server strikes the quadrant; this file only words it). It was 0.30, which on
// this book is looser than field-to-field noise, so a farm could show 0 unstable
// acres (Lawnel Farms 2, most variable field 0.228). Kevin approved 15% on 2026-11-01.
const YA_GOOD_CV    = 0.08; // at/below = very steady
const YA_HIGH_CV    = 0.15; // above = unstable (the quadrant cut)
const YA_SWING_CV   = 0.30; // above = erratic

// Bare unit for the yield columns, in the unit each crop is measured in: silage in
// WET tons (Kevin: "We always measure silage yields in wet tons per acre"), dry beans
// in pounds (insured in lb — INSURED_IN_LB in api/_jd.js), everything else bushels.
// Silage is its own crop on this page and never shares an average with grain.
const yaUnit = (crop) => (jdIsSilage(crop) ? "wet t" : /^DRY BEANS/i.test(String(crop || "")) ? "lb" : "bu");

// Kevin's long-standing framing, kept verbatim — above/below average crossed with
// stable/unstable. The internal keys are historical; the labels are what matter.
const YA_QUAD = {
  anchor:     { label: "Above Avg · Stable",   short: "Above · Stable",   color: "#2f7d32", bg: "#e8f4e6",
                note: "Above the operation average in this crop and dependable — your best ground for it" },
  boom:       { label: "Above Avg · Unstable", short: "Above · Unstable", color: "#8a5e10", bg: "#fbf1dc",
                note: "Beats the average in this crop but swings — real upside, and where insurance earns out" },
  structural: { label: "Below Avg · Stable",   short: "Below · Stable",   color: "#8c3a3a", bg: "#f8e9e9",
                note: "Reliably below average in this crop — a good year will not save it" },
  fragile:    { label: "Below Avg · Unstable", short: "Below · Unstable", color: "#6a4a8a", bg: "#efe9f6",
                note: "Below average in this crop and erratic — usually drainage or drought" },
};
// The overall, crop-agnostic view — a pseudo-crop key so it rides the same
// ranking and quadrant machinery as a real crop instead of a parallel code path.
const YA_ALL = "__ALL__";
const yaCropName = (c) => (c === YA_ALL ? "All crops" : c);
// What the stability boxes call ground that has a ranking but not yet a third year
// of that crop — the fifth box, so the five add up to every scored acre.
const YA_UNRATED = { label: "Not enough years yet", short: "Not rated yet", color: "#7d877b", bg: "#f1f3f0",
                     note: `Scored, but fewer than ${YA_MIN_CV_YEARS} years of this crop — stability needs a third season` };
const YA_BOX = { ...YA_QUAD, unrated: YA_UNRATED };

// Plain-English stability, so the CV number never has to be interpreted.
const yaSteadyWord = (cv) =>
  cv == null ? { word: "—",          color: "#8a8f8a" }
: cv <= YA_GOOD_CV ? { word: "Very steady", color: "#2f7d32" }
: cv <= YA_HIGH_CV ? { word: "Steady",      color: "#4a7c3f" }
: cv <= YA_SWING_CV ? { word: "Swingy",     color: "#8a5e10" }
                   : { word: "Erratic",     color: "#8c3a3a" };

// ─────────────────────────────────────────────────────────────────────────────
// YIELD BY VARIETY / BY PLANT DATE
//
// Two lenses over `/api/market?fn=jd-yieldfactors`. The server does the joining
// and the refusing (see hYieldFactors in api/_jd.js for why half the `variety`
// column isn't a variety); this draws it and — the part that matters — SAYS WHAT
// IT COULDN'T ANSWER.
//
// The chart is one series, so bar LENGTH carries the value and the variety name is
// a direct label on every row. Colour therefore carries no identity and none is
// invented: real varieties share one green, and "Not recorded" is deliberately the
// only grey on the page, because it is not a variety and must not read as one.
const YF_BAR = "#4a7c3f";        // real variety / real planting window
const YF_MUTED = "#8f9a8d";      // not recorded — reads grey ON PURPOSE
const YF_MIN_CELL_ACRES = 15;    // below this a cell is a sliver, not an observation
const YF_BAR_DOWN = "#a8563f";   // below the average it is being compared to

function YieldFactorsPanel({ lens, isMobile, mode, restrictFarmerIds, nameById }) {
  const isPortal = mode === "portal";
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [farmId, setFarmId] = useState("");
  const [crop, setCrop]     = useState("");
  const [yrs, setYrs]       = useState(() => new Set());
  const [qcOpen, setQcOpen] = useState(false);

  // Same scoping contract as the field index: NAME the caller's farms rather than
  // leaving the request open and trusting the server to narrow it, because
  // "Preview as" renders this with an ADMIN token and an unqualified admin call
  // correctly returns the whole book.
  const scopeQ = useMemo(() => {
    const ids = (restrictFarmerIds || []).filter(Boolean);
    return ids.length ? `&farmer=${ids.map(encodeURIComponent).join(",")}` : "";
  }, [restrictFarmerIds]);

  useEffect(() => {
    if (isPortal && !scopeQ) { setData({ rows: [], farms: [], dropped: null }); return; }
    let alive = true;
    authedFetch(`/api/market?fn=jd-yieldfactors${scopeQ}`)
      .then(r => r.json())
      .then(d => { if (!alive) return;
        if (!d || d.error) { setErr((d && d.error) || "no response"); return; }
        setData(d); })
      .catch(e => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [scopeQ, isPortal]);

  // Memoised: a fresh [] each render would re-fire every effect keyed on rows.
  const rows = useMemo(() => (data && data.rows) || [], [data]);
  const cropOptions = useMemo(() => {
    const c = {};
    rows.forEach(r => { if (!farmId || r.farmer_id === farmId) c[r.crop] = (c[r.crop] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }, [rows, farmId]);
  useEffect(() => {
    if (cropOptions.length && (!crop || !cropOptions.includes(crop))) setCrop(cropOptions[0]);
  }, [cropOptions, crop]);

  const farmOptions = useMemo(() => {
    const m = new Map();
    rows.forEach(r => m.set(r.farmer_id, (m.get(r.farmer_id) || 0) + 1));
    return [...m.entries()].map(([id, n]) => ({ id, n, name: (nameById && nameById.get(id)) || "Farm" }))
      .sort((a, b) => b.n - a.n);
  }, [rows, nameById]);

  const yearOptions = useMemo(() => [...new Set(rows
    .filter(r => (!farmId || r.farmer_id === farmId) && r.crop === crop)
    .map(r => r.yr))].sort((a, b) => b - a), [rows, farmId, crop]);

  // Planting reads ONE season at a time by default: the newest season with plant
  // dates. "All seasons" pools them, each field measured against its own farm's
  // average that year. Varieties keep every season, because a fair side-by-side
  // needs as many of them as there are.
  const isPlant = lens === "plantdate";
  useEffect(() => {
    // Functional, and a no-op when nothing changes, so it can never feed itself.
    const same = (a, b) => a.size === b.size && [...a].every(y => b.has(y));
    if (!isPlant) { setYrs(prev => prev.size ? new Set() : prev); return; }
    const ys = [...new Set(rows.filter(r => (!farmId || r.farmer_id === farmId) && r.crop === crop && r.plantDate)
      .map(r => r.yr))].sort((a, b) => b - a);
    const next = ys.length ? new Set([ys[0]]) : new Set();
    setYrs(prev => same(prev, next) ? prev : next);
  }, [isPlant, rows, farmId, crop]);

  // Every season of this crop on the farm(s) in view, whatever years are picked: a
  // field's ground record is judged from its other seasons.
  const cropRows = useMemo(() => rows.filter(r => (!farmId || r.farmer_id === farmId) && r.crop === crop),
    [rows, farmId, crop]);
  const inScope = useMemo(() => rows.filter(r =>
    (!farmId || r.farmer_id === farmId) && r.crop === crop && (!yrs.size || yrs.has(r.yr))),
    [rows, farmId, crop, yrs]);

  // Denominator = the farm's WHOLE year, including the fields with no planting
  // date. Averaging only the dated fields would measure them against themselves.
  // Each field is read against ITS OWN farm's average that season, because raw
  // bushels pooled across seasons rank the years, not the planting dates.
  const fyAvg = useMemo(() => {
    const m = new Map();
    for (const r of inScope) {
      const k = r.farmer_id + "|" + r.yr;
      const c = m.get(k) || { ac: 0, sum: 0, fields: new Set() };
      c.ac += r.acres; c.sum += r.yld * r.acres; c.fields.add(r.field || "f" + c.fields.size);
      m.set(k, c);
    }
    const out = new Map();
    for (const [k, c] of m) if (c.ac > 0) out.set(k, { avg: c.sum / c.ac, nFields: c.fields.size });
    return out;
  }, [inScope]);

  const unit = `${yaUnit(crop)}/ac`;

  // Provenance. The chart cannot tell a grower which John Deere host their numbers
  // came from, so the page does — see project_jd_sandbox_host_rows.
  const hostSplit = useMemo(() => {
    let sandbox = 0, production = 0;
    for (const r of inScope) { if (r.host === "sandbox") sandbox++; else if (r.host === "production") production++; }
    return { sandbox, production };
  }, [inScope]);

  if (err) return <div style={{ ...uiCard(), color: T.danger, fontSize: 13 }}>Couldn't load yield history: {err}</div>;
  if (!data) return <div style={{ ...uiCard(), color: T.faint, fontSize: 13 }}>Reading harvest history…</div>;
  if (!rows.length) return isPortal
    ? <JdConnectPanel isMobile={isMobile} restrictFarmerIds={restrictFarmerIds} what="harvest history"
        connectedTitle="No harvest history yet"
        connectedNote="Once John Deere sends a season of harvest data, your yields appear here broken out by the variety planted and by planting date." />
    : <div style={{ ...uiCard(), fontSize: 13, color: T.body }}>No John Deere harvest records in scope.</div>;

  const dropped = data.dropped;
  const droppedTotal = (dropped && dropped.total) || 0;

  return (
    <div>
      <div style={{ fontSize: 12.5, color: T.body, marginBottom: 12, maxWidth: 820, lineHeight: 1.55 }}>
        {lens === "variety"
          ? <>How each seed did against the others grown the same season. The variety comes from the harvest
             record, or from the planting pass on that same field and season where the harvest didn{"’"}t name one.</>
          : <>How each field yielded against when it was planted. The plant date comes from the planter, or
             from the insurance acreage report where the planter left none.</>}
      </div>

      {/* Filters in one row above the chart */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        {farmOptions.length > 1 && (
          <select value={farmId} onChange={e => setFarmId(e.target.value)}
            style={{ cursor: "pointer", maxWidth: 260, fontFamily: "inherit", fontSize: 12.5,
                     padding: "6px 10px", borderRadius: T.r.sm, border: `1px solid ${T.line}`,
                     background: T.surface, color: T.ink }}>
            <option value="">All farms ({farmOptions.length})</option>
            {farmOptions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
        {cropOptions.map(c => (
          <button key={c} onClick={() => setCrop(c)}
            style={{ cursor: "pointer", font: "inherit", padding: "6px 12px", borderRadius: 999,
                     border: `1px solid ${c === crop ? "#2d4a2d" : T.line}`,
                     background: c === crop ? "#2d4a2d" : T.surface, color: c === crop ? "#fff" : T.body,
                     fontSize: 12, fontWeight: c === crop ? 800 : 600 }}>{yaCropLabel(c)}</button>
        ))}
        {isPlant && yearOptions.length > 1 && (
          <span style={{ display: "inline-flex", gap: 4, marginLeft: 4, flexWrap: "wrap" }}>
            {yearOptions.map(y => {
              const on = yrs.size === 1 && yrs.has(y);
              return (
                <button key={y} onClick={() => setYrs(new Set([y]))} aria-pressed={on}
                  style={{ cursor: "pointer", font: "inherit", padding: "5px 9px", borderRadius: 6,
                           border: `1px solid ${on ? "#9dbd94" : T.line}`, background: on ? "#eef7e8" : T.surface,
                           color: on ? T.ink : T.faint, fontSize: 11.5, fontWeight: on ? 800 : 600 }}>{y}</button>
              );
            })}
            <button onClick={() => setYrs(new Set())} aria-pressed={yrs.size === 0}
              style={{ cursor: "pointer", font: "inherit", padding: "5px 9px", borderRadius: 6,
                       border: `1px solid ${yrs.size === 0 ? "#9dbd94" : T.line}`, background: yrs.size === 0 ? "#eef7e8" : T.surface,
                       color: yrs.size === 0 ? T.ink : T.faint, fontSize: 11.5, fontWeight: yrs.size === 0 ? 800 : 600 }}>All seasons</button>
          </span>
        )}
        {!isPlant && yearOptions.length > 1 && (
          <span style={{ display: "inline-flex", gap: 4, marginLeft: 4 }}>
            {yearOptions.map(y => {
              const on = yrs.has(y);
              return (
                <button key={y} onClick={() => setYrs(p => { const n = new Set(p); n.has(y) ? n.delete(y) : n.add(y); return n; })}
                  title={on ? `Showing ${y}` : `Add ${y}`}
                  style={{ cursor: "pointer", font: "inherit", padding: "5px 9px", borderRadius: 6,
                           border: `1px solid ${on ? "#9dbd94" : T.line}`, background: on ? "#eef7e8" : T.surface,
                           color: on ? T.ink : T.faint, fontSize: 11.5, fontWeight: on ? 800 : 600 }}>{y}</button>
              );
            })}
            {yrs.size > 0 && (
              <button onClick={() => setYrs(new Set())} style={{ cursor: "pointer", font: "inherit", border: "none",
                background: "none", color: T.green, fontSize: 11.5, fontWeight: 700 }}>all years</button>
            )}
          </span>
        )}
      </div>

      {hostSplit.sandbox > 0 && !isPortal && (
        <div style={{ ...uiCard(10), borderLeft: "4px solid #c08a2c", marginBottom: 12, fontSize: 12, color: T.body, lineHeight: 1.5 }}>
          <b style={{ color: T.ink }}>Data provenance:</b> {hostSplit.sandbox.toLocaleString()} of{" "}
          {(hostSplit.sandbox + hostSplit.production).toLocaleString()} harvests in view were pulled from John
          Deere's <b>sandbox</b> host rather than production. The field names and yield levels look genuine, but
          whether these are the grower's real operations is unconfirmed — treat this chart as provisional until
          Deere confirms.
        </div>
      )}

      {lens === "variety" ? (
        <YfFairVarieties inScope={inScope} cropRows={cropRows} crop={crop} unit={unit} isMobile={isMobile} />
      ) : (
        <YfPlantWindows inScope={inScope} fyAvg={fyAvg} crop={crop} unit={unit} isMobile={isMobile}
          season={yrs.size === 1 ? [...yrs][0] : null} />
      )}

      {lens === "plantdate" && (
        <YfPlantingVsDates inScope={inScope} fyAvg={fyAvg} crop={crop} unit={unit}
          rmaMeta={data.rma || null} isMobile={isMobile} />
      )}

      {droppedTotal > 0 && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => setQcOpen(o => !o)}
            style={{ cursor: "pointer", fontSize: 12, padding: "6px 11px", borderRadius: T.r.sm,
                     border: `1px solid ${qcOpen ? "#bcdcb4" : T.line}`, fontFamily: "inherit",
                     background: qcOpen ? "#eef7e8" : T.surface, color: T.body }}>
            {droppedTotal.toLocaleString()} machine record{droppedTotal === 1 ? "" : "s"} left out {qcOpen ? "▲" : "▼"}
          </button>
          {!qcOpen && (
            <span style={{ fontSize: 11.5, color: T.faint, marginLeft: 8 }}>
              partial passes and impossible readings, all crops and seasons
            </span>
          )}
          {qcOpen && (
            <div style={{ ...uiCard(12), marginTop: 8, fontSize: 12, color: T.body }}>
              <div style={{ marginBottom: 8, lineHeight: 1.5 }}>
                Machine records that couldn't stand as a field{"’"}s yield, such as a load that covered only
                part of the field or read an impossible number. They are left out of every chart here.
              </div>
              {Object.entries(dropped.byReason || {}).sort((a, b) => b[1] - a[1]).map(([why, n]) => (
                <div key={why} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0",
                                        borderBottom: `1px solid ${T.lineSoft}` }}>
                  <span>{why}</span><b style={{ color: T.ink }}>{n.toLocaleString()}</b>
                </div>
              ))}
              <div style={{ marginTop: 8, fontSize: 11, color: T.faint }}>
                {Math.round(dropped.acres || 0).toLocaleString()} acres in total.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PLANTING DATE AGAINST THE INSURANCE DATES, AND VARIETIES COMPARED FAIRLY
// (Kevin, 2026-11-01: "The yield analytics redesign is fantastic, build it all").
//
// Both read only what jd-yieldfactors serves: the field-year yield, the machine's own
// planting date and variety, and — for the planting view — RMA's own final planting
// date and end of the late planting period for that field's county, resolved by
// src/rmaPlantingWindow.js on the server. Nothing is estimated here and no dollar
// figure is shown. Every yield is measured against ITS OWN farm's average that year
// first, because raw yields pooled across seasons rank the years, not the dates or
// the seed.
// ─────────────────────────────────────────────────────────────────────────────
const YF_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Day of the year, so the same planting day in two seasons lands in the same place.
function yfDoy(iso) {
  const d = new Date(String(iso || "").slice(0, 10) + "T00:00:00Z");
  if (isNaN(d)) return null;
  return Math.round((d - Date.UTC(d.getUTCFullYear(), 0, 1)) / 864e5);
}
function yfDayLabel(iso) {
  const d = new Date(String(iso || "").slice(0, 10) + "T00:00:00Z");
  return isNaN(d) ? "—" : `${YF_MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}
// The status words and colours, one table, so the dots, the totals and the list agree.
const YF_RMA = {
  timely:    { label: "By the final planting date",   short: "On time",        color: "#2f7d32" },
  late:      { label: "In the late planting period",  short: "Late period",    color: "#c08a2c" },
  past_late: { label: "After the late planting period", short: "Past late period", color: "#a8453c" },
  early:     { label: "Before RMA's earliest planting date", short: "Early",   color: "#5b7fa6" },
  // Grey is a field WITH a plant date that has no RMA final planting date to be
  // judged by (that crop year's dates aren't loaded, or the county has none). It is
  // never a field missing its plant date: those aren't dots at all.
  unknown:   { label: "Planted, but no RMA date to judge it by", short: "No RMA date for that year", color: "#9aa59a" },
};

// ── YIELD BY PLANTING WINDOW ────────────────────────────────────────────────
// Kevin, 2026-11-01: "Not positive yield by planting date makes total sense either."
// Read in five seconds: one season by default, windows named the way an agent talks
// (judged against RMA's own final planting date for the field's county), one row per
// window, and the acres reconciled out loud: counted vs on file, and why the rest
// isn't in it. Where most of the season has no RMA date to judge by, the windows
// fall back to calendar weeks (one season) or day-of-month (pooled), and say so.
// Kevin, 2026-11-01: "I think by planting date should have more than one time and
// late." On-time planting is split into weeks counted back from the field's own
// final planting date (w0 = the final week, 0 to 6 days before it), and the late
// period always gets its row, even when nothing was planted in it.
const YF_WINDOWS = [
  { k: "early",     label: "Before the earliest date", color: "#5b7fa6" },
  { k: "w4",        label: "5+ weeks before",          color: "#1f5e23", wk: 4 },
  { k: "w3",        label: "4 weeks before",           color: "#2f7d32", wk: 3 },
  { k: "w2",        label: "3 weeks before",           color: "#4a8f3c", wk: 2 },
  { k: "w1",        label: "2 weeks before",           color: "#5f9a3e", wk: 1 },
  { k: "w0",        label: "Final week",               color: "#7aa646", wk: 0 },
  { k: "wx",        label: "On time, week unclear",    color: "#6f9b3c" },
  { k: "late",      label: "Late period",              color: "#c08a2c" },
  { k: "past_late", label: "After the late period",    color: "#a8453c" },
  { k: "unjudged",  label: "No RMA date to judge by",  color: "#9aa59a" },
];
// Days before the final planting date -> week bucket 0..4 (4 = five weeks or more).
const yfWeekBack = (d) => Math.min(4, Math.floor(Math.max(0, -d) / 7));
// Why fields with a plant date still aren't judged against RMA's dates, as one clause
// naming the biggest cause by acres. The server writes the reason on each row.
function yfUnjudgedWhy(pts, cropLabel) {
  const by = {};
  for (const p of pts) {
    if (yfRmaWindowOf(p) !== "unjudged") continue;
    const r = String((p.rma && p.rma.reason) || "");
    const m = r.match(/dates for (\d{4}) are not loaded/);
    const k = m ? "yr:" + m[1]
      : /for this state are not loaded/.test(r) ? "state"
      : /publishes no planting dates/.test(r) ? "none"
      : /no insured county/.test(r) ? "county"
      : /windows apply/.test(r) ? "several" : "other";
    by[k] = (by[k] || 0) + (p.acres || 0);
  }
  const keys = Object.keys(by);
  if (!keys.length) return null;
  const yrs = keys.filter(k => k.startsWith("yr:")).map(k => k.slice(3)).sort();
  const top = keys.sort((a, b) => by[b] - by[a])[0];
  const crop = String(cropLabel || "this crop").toLowerCase();
  const txt = top.startsWith("yr:")
      ? `RMA${"’"}s dates for ${yrs.length > 3 ? `${yrs[0]} to ${yrs[yrs.length - 1]}` : yrs.join(", ")} aren${"’"}t loaded yet`
    : top === "state" ? `RMA${"’"}s dates for this state aren${"’"}t loaded yet`
    : top === "none" ? `RMA publishes no final planting date for ${crop} in this county`
    : top === "county" ? "the field isn" + "’" + "t tied to an insured county"
    : top === "several" ? `RMA sets more than one final planting date for ${crop} here (by type or practice) and the records don${"’"}t say which applies`
    : "RMA" + "’" + "s window couldn" + "’" + "t be read for these fields";
  return keys.length > 1 ? `mostly because ${txt}` : `because ${txt}`;
}

function yfRmaWindowOf(r) {
  const st = r.rma && r.rma.status;
  if (st === "early") return "early";
  if (st === "late") return "late";
  if (st === "past_late") return "past_late";
  if (st === "timely") {
    if (r.rma.daysVsFpd == null) return "wx";
    const wk = yfWeekBack(r.rma.daysVsFpd);
    // Several RMA windows apply (type or practice not on file): name the week only
    // when the earliest and latest final planting dates put the field in the same one.
    if (r.rma.fpdMax && r.rma.fpdMax !== r.rma.fpd) {
      const t = (iso) => Date.parse(String(iso || "").slice(0, 10) + "T00:00:00Z");
      const dMax = Math.round((t(r.plantDate) - t(r.rma.fpdMax)) / 864e5);
      if (!Number.isFinite(dMax) || yfWeekBack(dMax) !== wk) return "wx";
    }
    return "w" + wk;
  }
  return "unjudged";
}
const YF_WIN_MIN_FIELDS = 3;     // a planting window with fewer fields is shown greyed
function YfPlantWindows({ inScope, fyAvg, crop, unit, isMobile, season }) {
  const cropLabel = yaCropLabel(crop);
  const onFile = inScope.reduce((s, r) => s + r.acres, 0);
  const dated = inScope.filter(r => r.plantDate);
  const undatedAc = onFile - dated.reduce((s, r) => s + r.acres, 0);

  // A farm-season with one field compares that field to itself (100% by
  // construction), so it is counted apart rather than drawn.
  let soloAc = 0;
  const pts = [];
  for (const r of dated) {
    const fa = fyAvg.get(r.farmer_id + "|" + r.yr);
    if (!fa || fa.nFields < 2 || !(fa.avg > 0)) { soloAc += r.acres; continue; }
    pts.push({ ...r, idx: r.yld / fa.avg });
  }
  const judgedAc = pts.filter(p => yfRmaWindowOf(p) !== "unjudged").reduce((s, p) => s + p.acres, 0);
  const ptsAc = pts.reduce((s, p) => s + p.acres, 0);
  const byRma = ptsAc > 0 && judgedAc / ptsAc >= 0.5;
  const unjudgedWhy = yfUnjudgedWhy(pts, cropLabel);

  const fpds = [...new Set(pts.flatMap(p => p.rma ? [p.rma.fpd, p.rma.fpdMax] : []).filter(Boolean))].sort();
  const fpdTxt = !fpds.length ? null
    : fpds.length === 1 || yfDayLabel(fpds[0]) === yfDayLabel(fpds[fpds.length - 1]) ? yfDayLabel(fpds[0])
    : `${yfDayLabel(fpds[0])} to ${yfDayLabel(fpds[fpds.length - 1])}, by county, type or practice`;

  const keyOf = (p) => byRma ? yfRmaWindowOf(p) : season ? yfWeekKey(p.plantDate) : yfWindowKey(p.plantDate);
  const m = new Map();
  for (const p of pts) {
    const k = keyOf(p);
    const c = m.get(k) || { k, ac: 0, ysum: 0, isum: 0, n: 0, ins: 0 };
    c.ac += p.acres; c.ysum += p.yld * p.acres; c.isum += p.idx * p.acres; c.n++;
    if (p.plantDateSrc === "insurance") c.ins += p.acres;
    m.set(k, c);
  }
  let cells = [...m.values()];
  if (byRma) {
    // Every week from the earliest one planted through the final week gets a row, and
    // the late period always does, so a gap or an empty late period reads as such.
    const have = new Set(cells.map(c => c.k));
    const top = Math.max(-1, ...cells.map(c => (YF_WINDOWS.find(w => w.k === c.k) || {}).wk).filter(n => n != null));
    const addEmpty = (k) => { if (!have.has(k)) cells.push({ k, ac: 0, ysum: 0, isum: 0, n: 0, ins: 0, empty: true }); };
    for (let i = 0; i <= top; i++) addEmpty("w" + i);
    if (cells.some(c => c.k !== "unjudged")) addEmpty("late");
    cells.sort((a, b) => YF_WINDOWS.findIndex(w => w.k === a.k) - YF_WINDOWS.findIndex(w => w.k === b.k));
  } else {
    cells.sort((a, b) => String(a.k).localeCompare(String(b.k)));
    // Slivers pool into one row, never vanish.
    const small = cells.filter(c => c.ac < YF_MIN_CELL_ACRES);
    if (small.length > 1) {
      cells = cells.filter(c => c.ac >= YF_MIN_CELL_ACRES);
      cells.push(small.reduce((t, c) => ({ ...t, ac: t.ac + c.ac, ysum: t.ysum + c.ysum, isum: t.isum + c.isum, n: t.n + c.n, ins: t.ins + c.ins }),
        { k: "small", ac: 0, ysum: 0, isum: 0, n: 0, ins: 0, label: `${small.length} small windows` }));
    }
  }
  const filled = cells.filter(c => !c.empty);
  const counted = cells.reduce((s, c) => s + c.ac, 0);
  // The dates each week covers, when one final planting date holds for every field
  // shown (same month and day in every season), e.g. "May 27–Jun 2".
  const sameLbl = (xs) => xs.length > 0 && xs.every(x => yfDayLabel(x) === yfDayLabel(xs[0]));
  const fpdOne = sameLbl(fpds) ? fpds[0] : null;
  const lateEnds = [...new Set(pts.map(p => p.rma && p.rma.lateEnd).filter(Boolean))];
  const shiftLbl = (iso, days) => { const d = new Date(String(iso).slice(0, 10) + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + days); return yfDayLabel(d.toISOString()); };
  const datesOf = (c) => {
    if (!byRma || !fpdOne) return null;
    const w = YF_WINDOWS.find(x => x.k === c.k);
    if (w && w.wk != null) return w.wk === 4 ? `by ${shiftLbl(fpdOne, -28)}` : `${shiftLbl(fpdOne, -(7 * w.wk + 6))}${"–"}${shiftLbl(fpdOne, -7 * w.wk)}`;
    if (c.k === "late" && sameLbl(lateEnds)) return `${shiftLbl(fpdOne, 1)}${"–"}${yfDayLabel(lateEnds[0])}`;
    return null;
  };
  const insAc = pts.filter(p => p.plantDateSrc === "insurance").reduce((s, p) => s + p.acres, 0);
  const maxDev = filled.reduce((mx, c) => Math.max(mx, Math.abs(c.isum / c.ac - 1)), 0) || 0.05;
  const ac = (n) => Math.round(n).toLocaleString("en-US");
  const labelOf = (c) => c.label
    || (byRma ? YF_WINDOWS.find(w => w.k === c.k).label
       : season ? yfWeekLabel(c.k).replace(/, \d{4}$/, "") : yfWindowLabel(c.k).replace("\n", " "));
  const colorOf = (c, dev) => byRma ? YF_WINDOWS.find(w => w.k === c.k).color : dev >= 0 ? YF_BAR : YF_BAR_DOWN;

  // Why the rest isn't counted, in one line, biggest reason first.
  const why = {};
  for (const r of inScope) if (!r.plantDate) why[r.noDateWhy || "no planting pass"] = (why[r.noDateWhy || "no planting pass"] || 0) + r.acres;
  const topWhy = Object.entries(why).sort((a, b) => b[1] - a[1])[0];
  const notCounted = [
    undatedAc >= 0.5 && `${ac(undatedAc)} ac have no plant date from the planter or the acreage report` +
      (topWhy ? ` (mostly: ${topWhy[0]})` : ""),
    soloAc >= 0.5 && `${ac(soloAc)} ac were the farm${"’"}s only field that season, with nothing to compare against`,
  ].filter(Boolean);

  if (!onFile) return null;
  return (
    <div style={{ ...uiCard(isMobile ? 12 : 16), marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>
        {cropLabel} yield by planting date {"·"} {season || "All seasons"}
      </div>
      <div style={{ fontSize: 12, color: T.body, marginTop: 3, lineHeight: 1.5 }}>
        {byRma
          ? <>Grouped against RMA{"’"}s final planting date{fpdTxt ? ` (${fpdTxt})` : ""}. Each window is measured against the farm{"’"}s own average {season ? "that season" : "each season"}.</>
          : <>{season ? "Grouped by the week planted" : "Grouped by part of the month planted"}, measured against the farm{"’"}s own average {season ? "that season" : "each season"}.{" "}
             {unjudgedWhy ? <>Most fields aren{"’"}t judged against RMA{"’"}s final planting date, {unjudgedWhy}.</> : null}</>}
      </div>

      {!filled.length ? (
        <div style={{ fontSize: 12.5, color: T.body, marginTop: 10 }}>
          No {cropLabel.toLowerCase()} field in this selection has both a plant date and another field to compare it with.
        </div>
      ) : filled.length === 1 ? (
        // One window holds every dated field: it IS the farm average, so a bar would
        // say nothing. Say what happened instead.
        <div style={{ fontSize: 12.5, color: T.body, marginTop: 10, lineHeight: 1.5 }}>
          Every dated field fell in one window ({labelOf(filled[0])}, {ac(filled[0].ac)} ac, {yaFmtYld(filled[0].ysum / filled[0].ac, crop)} {unit}), so there{"’"}s no earlier or later planting to compare it with.
        </div>
      ) : (
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: `minmax(${isMobile ? 92 : 150}px, auto) minmax(0, 1fr) auto`,
                      columnGap: 10, rowGap: 9, alignItems: "center" }}>
          {cells.map(c => {
            const dates = datesOf(c);
            if (c.empty) return (
              <React.Fragment key={c.k}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: T.faint, lineHeight: 1.2 }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 8, border: `1.5px solid ${colorOf(c)}`, boxSizing: "border-box", marginRight: 6 }} />
                    {labelOf(c)}
                  </div>
                  <div style={{ fontSize: 11, color: T.faint }}>{dates ? `${dates} ${"·"} ` : ""}nothing planted</div>
                </div>
                <div style={{ position: "relative", height: 16 }} aria-hidden="true">
                  <div style={{ position: "absolute", left: "50%", top: -3, bottom: -3, borderLeft: `1px dashed ${T.lineStrong}` }} />
                </div>
                <div style={{ textAlign: "right", fontSize: 13, color: T.faint }}>{"—"}</div>
              </React.Fragment>
            );
            const idx = c.isum / c.ac, dev = idx - 1, up = dev >= 0;
            const w = Math.max(1.5, Math.abs(dev) / maxDev * 48);
            const devTxt = Math.abs(dev) < 0.005 ? "even" : `${up ? "+" : "−"}${Math.abs(Math.round(dev * 100))}%`;
            // One or two fields is an anecdote, not a pattern: drawn grey, number kept.
            const few = c.n < YF_WIN_MIN_FIELDS;
            return (
              <React.Fragment key={c.k}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
                    {byRma && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 8, background: colorOf(c), marginRight: 6 }} />}
                    {labelOf(c)}
                  </div>
                  <div style={{ fontSize: 11, color: T.faint }}>{dates ? `${dates} ${"·"} ` : ""}{ac(c.ac)} ac {"·"} {c.n} field{c.n === 1 ? "" : "s"}{few ? ", too few to read" : ""}</div>
                </div>
                <div style={{ position: "relative", height: 16 }} aria-hidden="true">
                  <div style={{ position: "absolute", left: "50%", top: -3, bottom: -3, borderLeft: `1px dashed ${T.lineStrong}` }} />
                  <div style={{ position: "absolute", top: 3, height: 10, borderRadius: 3,
                                left: up ? "50%" : `${50 - w}%`, width: `${w}%`,
                                background: c.k === "unjudged" || few ? YF_MUTED : up ? YF_BAR : YF_BAR_DOWN }} />
                </div>
                <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: few || Math.abs(dev) < 0.005 ? T.faint : up ? "#2f7d32" : "#8c3a3a",
                                fontVariantNumeric: "tabular-nums" }}>{devTxt}</div>
                  <div style={{ fontSize: 11, color: T.faint, fontVariantNumeric: "tabular-nums" }}>{yaFmtYld(c.ysum / c.ac, crop)} {unit}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 12, paddingTop: 9, borderTop: `1px solid ${T.lineSoft}`, fontSize: 11.5, color: T.body, lineHeight: 1.55 }}>
        <b style={{ color: T.ink }}>{ac(counted)} of {ac(onFile)} ac counted.</b>
        {notCounted.length > 0 && <> Not counted: {notCounted.join("; ")}.</>}
        {insAc >= 0.5 && (counted - insAc < 0.5
          ? <> Every plant date here comes from the acreage report; the planter recorded none.</>
          : <> Plant dates for {ac(insAc)} ac come from the acreage report, the rest from the planter.</>)}
        {" "}Center line is the farm average; it{"’"}s a pattern to ask about, not a trial.
      </div>
    </div>
  );
}

// ── PLANTING ────────────────────────────────────────────────────────────────
function YfPlantingVsDates({ inScope, fyAvg, crop, unit, rmaMeta, isMobile }) {
  const [pick, setPick] = useState(null);
  // A field-year counts only where its farm-year holds a second field: one field
  // indexed against itself is 100% by construction and says nothing.
  const pts = useMemo(() => inScope.filter(r => r.plantDate).map(r => {
    const fa = fyAvg.get(r.farmer_id + "|" + r.yr);
    if (!fa || fa.nFields < 2 || !(fa.avg > 0)) return null;
    const doy = yfDoy(r.plantDate);
    if (doy == null) return null;
    const st = (r.rma && r.rma.status) || "unknown";
    return { ...r, idx: r.yld / fa.avg, doy, st: YF_RMA[st] ? st : "unknown" };
  }).filter(Boolean), [inScope, fyAvg]);

  if (pts.length < 3) return null;

  // RMA's dates for the points in view. One date draws a line; counties that differ
  // draw the spread, and the label says so rather than picking one.
  const fpds = [...new Set(pts.map(p => p.rma && p.rma.fpd).filter(Boolean))].sort();
  const ends = [...new Set(pts.map(p => p.rma && p.rma.lateEnd).filter(Boolean))].sort();
  const fpdYears = [...new Set(fpds.map(d => d.slice(0, 4)))];
  const fpdLo = fpds.length ? yfDoy(fpds[0]) : null, fpdHi = fpds.length ? yfDoy(fpds[fpds.length - 1]) : null;
  const endHi = ends.length ? yfDoy(ends[ends.length - 1]) : null;
  const dateYear = [...new Set(pts.filter(p => p.rma && p.rma.fpd).map(p => p.yr))].sort();

  const W = 640, H = isMobile ? 250 : 260, L = 40, R = 10, Tp = 14, B = 30;
  // The axis spans the plantings and the final planting date. The late period is drawn
  // to the edge and labelled with its end, rather than stretching the axis to it and
  // crowding every dot into one corner (corn's late period runs ~25 days past the FPD).
  const xs = pts.map(p => p.doy).concat(fpdLo != null ? [fpdLo, fpdHi] : []);
  const x0 = Math.min(...xs) - 4, x1 = Math.max(...xs) + 8;
  const xc = d => Math.min(x1, Math.max(x0, d));
  const ixs = pts.map(p => p.idx);
  const y0 = Math.max(0.4, Math.min(0.8, Math.min(...ixs) - 0.04)), y1 = Math.min(1.6, Math.max(1.2, Math.max(...ixs) + 0.04));
  const X = d => L + (d - x0) / Math.max(1, x1 - x0) * (W - L - R);
  const Y = v => Tp + (1 - (Math.max(y0, Math.min(y1, v)) - y0) / (y1 - y0)) * (H - Tp - B);
  // Month ticks inside the range.
  const ticks = [];
  for (let m = 0; m < 12; m++) for (const day of [1, 15]) {
    const doy = Math.round((Date.UTC(2025, m, day) - Date.UTC(2025, 0, 1)) / 864e5);
    if (doy > x0 && doy < x1) ticks.push({ doy, label: `${YF_MONTHS[m]} ${day}` });
  }
  const yTicks = [0.8, 0.9, 1, 1.1, 1.2].filter(v => v >= y0 && v <= y1);

  const late = pts.filter(p => p.st === "late" || p.st === "past_late").sort((a, b) => b.rma.daysVsFpd - a.rma.daysVsFpd);
  const cropWord = yaCropLabel(crop).toLowerCase();

  return (
    <div style={{ ...uiCard(isMobile ? 12 : 16), marginBottom: 12 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: T.ink }}>Every field, by plant date</div>
      <div style={{ fontSize: 12, color: T.body, marginTop: 3, lineHeight: 1.5, maxWidth: 760 }}>
        One dot per field per season: planted across, {cropWord} yield against the farm{"’"}s average up.
        Tap a dot for the field.
        {fpds.length > 0 && <> Shading is RMA{"’"}s final planting date and late period{dateYear.length ? ` (crop year ${dateYear.join(", ")})` : ""}.</>}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`${yaCropLabel(crop)} yield against planting date`}
           style={{ display: "block", marginTop: 8 }}>
        {fpdLo != null && endHi != null && (
          <rect x={X(fpdHi)} y={Tp} width={Math.max(0, X(xc(endHi)) - X(fpdHi))} height={H - Tp - B} fill="#f6ead2" />
        )}
        {endHi != null && endHi < x1 && (
          <rect x={X(endHi)} y={Tp} width={W - R - X(endHi)} height={H - Tp - B} fill="#f5dcd8" />
        )}
        {fpdLo != null && (fpdHi > fpdLo
          ? <rect x={X(fpdLo)} y={Tp} width={X(fpdHi) - X(fpdLo)} height={H - Tp - B} fill="#e6d3a8" opacity={0.7} />
          : <line x1={X(fpdLo)} x2={X(fpdLo)} y1={Tp} y2={H - B} stroke="#8a5e10" strokeWidth={1.5} strokeDasharray="4 3" />)}
        {fpdLo != null && (
          <text x={X(fpdLo) - 4} y={Tp + 11} textAnchor="end" fontSize={10.5} fontWeight={700} fill="#8a5e10">
            Final planting {fpdHi > fpdLo ? `${yfDayLabel(fpds[0])}–${yfDayLabel(fpds[fpds.length - 1])}` : yfDayLabel(fpds[0])}
          </text>
        )}
        {endHi != null && fpdHi != null && W - R - X(fpdHi) > 90 && (
          <text x={X(fpdHi) + 4} y={H - B - 6} fontSize={10.5} fontWeight={700} fill="#8a5e10">Late period to {yfDayLabel(ends[ends.length - 1])}</text>
        )}
        <line x1={L} x2={W - R} y1={Y(1)} y2={Y(1)} stroke={T.faint} strokeDasharray="3 3" />
        {yTicks.map(v => <text key={v} x={L - 6} y={Y(v) + 3.5} textAnchor="end" fontSize={10.5} fill={T.faint}>{Math.round(v * 100)}%</text>)}
        {ticks.map(t => <text key={t.doy} x={X(t.doy)} y={H - 10} textAnchor="middle" fontSize={10.5} fill={T.faint}>{t.label}</text>)}
        {pts.map((p, i) => {
          const on = pick && pick.farmer_id === p.farmer_id && pick.field === p.field && pick.yr === p.yr;
          return (
            <circle key={i} cx={X(p.doy)} cy={Y(p.idx)} r={Math.max(3, Math.min(9, Math.sqrt(p.acres) * 0.7))}
              fill={YF_RMA[p.st].color} fillOpacity={on ? 1 : 0.72} stroke={on ? T.ink : "#fff"} strokeWidth={on ? 2 : 1}
              style={{ cursor: "pointer" }} tabIndex={0} role="button"
              aria-label={`${p.field || "Field"} ${p.yr}, planted ${yfDayLabel(p.plantDate)}, ${Math.round(p.idx * 100)}% of the farm average`}
              onClick={() => setPick(p)} onKeyDown={e => { if (e.key === "Enter") setPick(p); }} />
          );
        })}
      </svg>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11.5, color: T.body, marginTop: 4 }}>
        {Object.entries(YF_RMA).filter(([k]) => pts.some(p => p.st === k)).map(([k, v]) => (
          <span key={k}><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 9, background: v.color, marginRight: 5 }} />{v.short}</span>
        ))}
      </div>

      {pick && (
        <div style={{ marginTop: 8, padding: "8px 10px", background: "#f7faf7", border: `1px solid ${T.line}`, borderRadius: 10, fontSize: 12.5, color: T.body }}>
          <b style={{ color: T.ink }}>{pick.field || "Field"} · {pick.yr}</b> · planted {yfDayLabel(pick.plantDate)}{pick.plantDateSrc === "insurance" ? " (acreage report)" : " (planter)"} ·{" "}
          {yaFmtYld(pick.yld, crop)} {unit} · {Math.round(pick.idx * 100)}% of the farm average · {Math.round(pick.acres)} ac
          <div style={{ color: YF_RMA[pick.st].color, fontWeight: 700, marginTop: 2 }}>
            {pick.rma && pick.rma.fpd && pick.st !== "unknown"
              ? (pick.rma.daysVsFpd > 0 ? `${pick.rma.daysVsFpd} days after` : pick.rma.daysVsFpd < 0 ? `${-pick.rma.daysVsFpd} days before` : "On")
                + ` the final planting date (${yfDayLabel(pick.rma.fpd)})` + (pick.st === "past_late" ? ", after the late planting period ended" : pick.st === "late" ? ", in the late planting period" : "")
              : (pick.rma && pick.rma.reason) || "No insurance dates for this field"}
          </div>
        </div>
      )}

      {late.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: T.ink, marginBottom: 4 }}>Planted after the final planting date</div>
          {late.slice(0, 12).map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 8, justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${T.lineSoft}`, fontSize: 12.5, color: T.body }}>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <b style={{ color: T.ink }}>{p.field || "Field"}</b> · {p.yr} · planted {yfDayLabel(p.plantDate)}
              </span>
              <span style={{ whiteSpace: "nowrap", color: YF_RMA[p.st].color, fontWeight: 700 }}>
                {p.rma.daysVsFpd} day{p.rma.daysVsFpd === 1 ? "" : "s"} late · {Math.round(p.idx * 100)}% of farm avg
              </span>
            </div>
          ))}
          {late.length > 12 && <div style={{ fontSize: 11.5, color: T.faint, marginTop: 4 }}>and {late.length - 12} more</div>}
          <div style={{ fontSize: 11.5, color: T.faint, marginTop: 6, lineHeight: 1.5 }}>
            RMA reduces the guarantee on acres planted in the late planting period, and acres planted after it
            ends are not insurable as planted for that crop year. The policy and the acreage report settle what applies.
          </div>
        </div>
      )}

      {pts.some(p => p.st === "unknown") && <div style={{ marginTop: 10, fontSize: 11, color: T.faint, lineHeight: 1.5 }}>
        Grey dots are fields with a plant date that isn{"’"}t judged against RMA{"’"}s dates, {yfUnjudgedWhy(pts, yaCropLabel(crop)) || "because no RMA window applies"}.
      </div>}
    </div>
  );
}

// ── VARIETIES, COMPARED FAIRLY ──────────────────────────────────────────────
// Kevin, 2026-11-01: "make sure yield by variety is defensible. That needs to be able
// to withstand major skepticism and critique".
//
// THE METHOD, which the page states in the same plain words:
//  1. Clean yields only. A field-season counts when it has ONE variety, the planter and
//     the combine agree on what it was, harvest moisture sat in the crop's normal band,
//     and the pass covered the field (the server's coverage + plausibility gate).
//  2. Same farm, same season. A variety is compared only with the OTHER varieties grown
//     on that farm that season: same weather, same operator, same combine. Never across
//     crops, and corn silage and corn grain are separate crops here.
//  3. Ground taken out. Each field's yield is divided by its own track record, its
//     yield against the farm average in its OTHER seasons of that crop (never the season
//     being judged), so seed on good ground gets no credit for the ground. A field needs
//     YF_VAR_MIN_HISTORY such seasons, and that record is pulled toward the farm average
//     the fewer seasons it has (n / (n + 1)).
//  4. Each field's result = its ground-adjusted yield / the ground-adjusted yield of the
//     other varieties beside it that season. A variety's number is the acre-weighted
//     average of those, with a 90% range. The range is the wider of the field-to-field
//     and the season-to-season spread, so two lucky fields in one good year can't make it
//     narrow.
//  5. Called only on real evidence: YF_VAR_MIN_FIELDS fields over YF_VAR_MIN_SEASONS
//     seasons. "Ahead" or "Behind" only when the whole range sits on one side of even;
//     anything thinner is "Not enough to call yet", never a ranking.
// No strip trials exist in this data (no field-season carries two harvested varieties),
// so side-by-side on one farm in one season is the closest comparison the data allows.
const YF_VAR_MIN_FIELDS = 5, YF_VAR_MIN_SEASONS = 2, YF_VAR_MIN_HISTORY = 2;
// A call needs its 90% range within ±25 points of its own average.
const YF_VAR_MAX_HALF = 0.25;
// One-sided 95% t (two-sided 90%), by degrees of freedom.
const YF_T90 = [Infinity, 6.314, 2.920, 2.353, 2.132, 2.015, 1.943, 1.895, 1.860, 1.833, 1.812,
  1.796, 1.782, 1.771, 1.761, 1.753, 1.746, 1.740, 1.734, 1.729, 1.725];
const yfT90 = (df) => df < 1 ? Infinity : df <= 20 ? YF_T90[df] : df <= 30 ? 1.70 : df <= 60 ? 1.67 : 1.645;
// Spellings of one product are one variety: case, spaces, dashes, an export's "~1",
// a brand prefix in front of the number (AG, DK, DKC) and the crop's name typed after
// it ("DKC49-72" = "49-72 Corn" = "49-72", "20x9" = "20X9"). Anything that could be a
// different product stays apart: seed treatments and add-ons ("24XF4 HEADSUP",
// "56-26 PIVOT BIO", "46-20 RIB") are their own rows. The most-planted spelling shows.
const yfVarKey = (v) => String(v || "").toUpperCase().replace(/~\d+$/, "").replace(/\s*\(\d+\)$/, "").replace(/[^A-Z0-9]/g, "")
  .replace(/^(AG|DKC|DK)(?=\d)/, "").replace(/(CORN|SOYBEANS?|SOY)$/, "");
const yfFieldKey = (r) => r.farmer_id + "|" + (r.jd_field_id != null ? "j" + r.jd_field_id : "n" + (r.field || ""));

function yfVarietyEvidence(inScope, cropRows) {
  // The season baseline for the ground record: every clean field of this crop on the
  // farm that season, named variety or not.
  const fs = new Map();
  for (const r of cropRows) {
    const k = r.farmer_id + "|" + r.yr;
    const c = fs.get(k) || { ac: 0, sum: 0, n: 0 };
    c.ac += r.acres; c.sum += r.yld * r.acres; c.n++;
    fs.set(k, c);
  }
  const hist = new Map();               // field -> [{ yr, idx }]
  for (const r of cropRows) {
    const c = fs.get(r.farmer_id + "|" + r.yr);
    if (!c || c.n < YA_MIN_PEERS || !(c.ac > 0)) continue;
    const k = yfFieldKey(r);
    if (!hist.has(k)) hist.set(k, []);
    hist.get(k).push({ yr: r.yr, idx: r.yld / (c.sum / c.ac) });
  }

  const out = { onFile: 0, used: 0, left: { noVariety: 0, conflict: 0, moisture: 0, noHistory: 0, alone: 0 } };
  const usable = [];
  for (const r of inScope) {
    out.onFile += r.acres;
    // A corn or soybean product always carries a number ("P1089AMXT", "46-20", "20X9");
    // a name without one ("bmr", "conventional") is a trait or a note, not a variety.
    const numbered = !/^(CORN|CORN SILAGE|SOYBEANS)$/.test(String(r.crop || "").toUpperCase()) || /\d/.test(String(r.variety || ""));
    if (!r.variety || r.varietySplit || !numbered) { out.left.noVariety += r.acres; continue; }
    if (r.varietyConflict) { out.left.conflict += r.acres; continue; }
    if (r.moistOk === false) { out.left.moisture += r.acres; continue; }
    const others = (hist.get(yfFieldKey(r)) || []).filter(h => h.yr !== r.yr);
    if (others.length < YF_VAR_MIN_HISTORY) { out.left.noHistory += r.acres; continue; }
    const m = others.reduce((s, h) => s + h.idx, 0) / others.length;
    const g = 1 + (m - 1) * others.length / (others.length + 1);
    usable.push({ ...r, g, adj: r.yld / g });
  }

  const byFs = new Map();
  for (const r of usable) {
    const k = r.farmer_id + "|" + r.yr;
    if (!byFs.has(k)) byFs.set(k, []);
    byFs.get(k).push(r);
  }
  const vs = new Map();
  const spell = new Map();               // key -> Map(spelling -> acres)
  for (const r of usable) {
    const k = yfVarKey(r.variety);
    if (!spell.has(k)) spell.set(k, new Map());
    spell.get(k).set(r.variety, (spell.get(k).get(r.variety) || 0) + r.acres);
    r.vk = k;
  }
  const shown = (k) => [...spell.get(k).entries()].sort((a, b) => b[1] - a[1])[0][0].replace(/~\d+$/, "").replace(/\s*\(\d+\)$/, "").replace(/\s+(corn|soybeans?|soy)$/i, "");
  for (const [fsKey, rs] of byFs) {
    const names = new Set(rs.map(r => r.vk));
    if (names.size < 2) { out.left.alone += rs.reduce((s, r) => s + r.acres, 0); continue; }
    for (const name of names) {
      const mine = rs.filter(r => r.vk === name), them = rs.filter(r => r.vk !== name);
      const tAc = them.reduce((s, r) => s + r.acres, 0);
      const base = them.reduce((s, r) => s + r.adj * r.acres, 0) / tAc;
      const v = vs.get(name) || { name: shown(name), pts: [], partners: new Map() };
      for (const r of mine) v.pts.push({ s: r.adj / base, ac: r.acres, fs: fsKey, yr: r.yr, farmer: r.farmer_id, yld: r.yld });
      for (const r of them) v.partners.set(shown(r.vk), (v.partners.get(shown(r.vk)) || 0) + r.acres);
      vs.set(name, v);
    }
    out.used += rs.reduce((s, r) => s + r.acres, 0);
  }

  const list = [...vs.values()].map(v => {
    const n = v.pts.length, ac = v.pts.reduce((s, p) => s + p.ac, 0);
    const mean = v.pts.reduce((s, p) => s + p.s * p.ac, 0) / ac;
    const yld = v.pts.reduce((s, p) => s + p.yld * p.ac, 0) / ac;
    const seasons = new Set(v.pts.map(p => p.yr)).size, farms = new Set(v.pts.map(p => p.farmer)).size;
    const sd = (a) => { const m = a.reduce((x, y) => x + y, 0) / a.length; return Math.sqrt(a.reduce((x, y) => x + (y - m) ** 2, 0) / (a.length - 1)); };
    const seField = n > 1 ? sd(v.pts.map(p => p.s)) / Math.sqrt(n) : Infinity;
    const cl = new Map(); for (const p of v.pts) { const c = cl.get(p.fs) || { ac: 0, sum: 0 }; c.ac += p.ac; c.sum += p.s * p.ac; cl.set(p.fs, c); }
    const cm = [...cl.values()].map(c => c.sum / c.ac);
    const seClus = cm.length > 1 ? sd(cm) / Math.sqrt(cm.length) : Infinity;
    // The wider of the two, at the fewer degrees of freedom: never the flattering one.
    const useClus = seClus >= seField;
    const se = Math.max(seField, seClus), df = useClus ? cm.length - 1 : n - 1;
    const half = Number.isFinite(se) ? yfT90(df) * se : Infinity;
    const lo = mean - half, hi = mean + half;
    // Enough fields over enough seasons, AND a range tight enough to mean something:
    // "no clear difference" from -60% to +190% says nothing and is not shown as a call.
    const thinWhy = n < YF_VAR_MIN_FIELDS ? "few fields" : seasons < YF_VAR_MIN_SEASONS ? "one season"
      : !(half <= YF_VAR_MAX_HALF) ? "results too scattered" : null;
    const verdict = thinWhy ? "thin" : lo > 1 ? "ahead" : hi < 1 ? "behind" : "even";
    const partners = [...v.partners.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
    return { name: v.name, n, ac, mean, yld, lo, hi, seasons, farms, farmSeasons: cm.length, verdict, thinWhy, partners };
  });
  const order = { ahead: 0, even: 1, behind: 2, thin: 3 };
  list.sort((a, b) => order[a.verdict] - order[b.verdict] || (a.verdict === "thin" ? a.name.localeCompare(b.name) : b.mean - a.mean));
  out.list = list;
  return out;
}

function YfFairVarieties({ inScope, cropRows, crop, unit, isMobile }) {
  const [howOpen, setHowOpen] = useState(false);
  const ev = useMemo(() => yfVarietyEvidence(inScope, cropRows || inScope), [inScope, cropRows]);
  const cropWord = yaCropLabel(crop).toLowerCase();
  const ac = (n) => Math.round(n).toLocaleString("en-US");
  // A bound that rounds to 0% still says which side of even it is on, so "Behind,
  // likely between −20% and even" can never read as a contradiction.
  const pct = (x) => {
    const raw = (x - 1) * 100, d = Math.round(raw);
    if (d !== 0) return `${d > 0 ? "+" : "−"}${Math.abs(d)}%`;
    return Math.abs(raw) < 0.05 ? "even" : raw > 0 ? "just above even" : "just below even";
  };
  const cropSing = ({ SOYBEANS: "soybean", "DRY BEANS": "dry bean", OATS: "oat", "CORN SILAGE": "corn silage", CORN: "corn grain" })[String(crop || "").toUpperCase()] || cropWord;
  const [allThin, setAllThin] = useState(false);
  const called = ev.list.filter(v => v.verdict !== "thin");
  const thin = ev.list.filter(v => v.verdict === "thin");
  const left = ev.left;
  const leftParts = [
    left.noVariety >= 0.5 && `${ac(left.noVariety)} ac with no seed product recorded (or a trait like BMR in its place), or more than one on the field`,
    left.conflict >= 0.5 && `${ac(left.conflict)} ac where the planter and the combine name different varieties`,
    left.moisture >= 0.5 && `${ac(left.moisture)} ac harvested outside the normal moisture range`,
    left.noHistory >= 0.5 && `${ac(left.noHistory)} ac on fields without ${YF_VAR_MIN_HISTORY} other seasons of ${cropWord} to judge the ground by`,
    left.alone >= 0.5 && `${ac(left.alone)} ac where only one variety qualified on the farm that season`,
  ].filter(Boolean);

  // The scale is fixed around even so a range never draws wider than the card.
  const span = Math.min(0.5, Math.max(0.1, ...called.map(v => Math.max(Math.abs(v.lo - 1), Math.abs(v.hi - 1))).filter(Number.isFinite)));
  const X = (x) => 50 + Math.max(-1, Math.min(1, (x - 1) / span)) * 48;
  const VERDICT = {
    ahead:  { word: "Ahead",               color: "#2f7d32" },
    even:   { word: "No clear difference", color: T.body },
    behind: { word: "Behind",              color: "#8c3a3a" },
  };

  return (
    <div style={{ ...uiCard(isMobile ? 12 : 16) }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{cropSing.charAt(0).toUpperCase() + cropSing.slice(1)} varieties, side by side</div>
      <div style={{ fontSize: 12, color: T.body, marginTop: 3, lineHeight: 1.5, maxWidth: 760 }}>
        Each variety is compared with the other varieties on the same farm the same season, after allowing
        for how well each field normally yields, so a seed isn{"’"}t credited for the ground it was put on.
        A variety is only called with {YF_VAR_MIN_FIELDS}+ fields over {YF_VAR_MIN_SEASONS}+ seasons.
      </div>

      {called.length > 0 ? (
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          {called.map(v => (
            <div key={v.name} style={{ minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                <span style={{ fontSize: 13.5, fontWeight: 800, color: T.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v.name}>{v.name}</span>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: VERDICT[v.verdict].color, whiteSpace: "nowrap" }}>
                  {VERDICT[v.verdict].word}
                </span>
              </div>
              <div style={{ position: "relative", height: 18, margin: "4px 0 2px" }} aria-hidden="true">
                <div style={{ position: "absolute", left: 0, right: 0, top: 8, borderTop: `1px solid ${T.lineSoft}` }} />
                <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, borderLeft: `1px dashed ${T.lineStrong}` }} />
                <div style={{ position: "absolute", top: 6, height: 5, borderRadius: 3, background: v.verdict === "ahead" ? "#9cc79a" : v.verdict === "behind" ? "#e0aaa0" : "#cfd8cd",
                              left: `${X(v.lo)}%`, width: `${Math.max(0.5, X(v.hi) - X(v.lo))}%` }} />
                <div style={{ position: "absolute", top: 3, width: 11, height: 11, borderRadius: 11, marginLeft: -5.5, left: `${X(v.mean)}%`,
                              background: VERDICT[v.verdict].color, border: "2px solid #fff" }} />
              </div>
              <div style={{ fontSize: 11.5, color: T.faint, lineHeight: 1.45 }}>
                {pct(v.mean)} on average, likely between {pct(v.lo)} and {pct(v.hi)} {"·"} {v.n} fields compared over {v.seasons} seasons, {ac(v.ac)} ac
                {v.partners.length > 0 && <> {"·"} against {v.partners.slice(0, 2).join(", ")}{v.partners.length > 2 ? ` and ${v.partners.length - 2} more` : ""}</>}
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: T.faint, display: "flex", justifyContent: "space-between" }}>
            <span>worse</span><span>even with the varieties beside it</span><span>better</span>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: T.body, marginTop: 10, lineHeight: 1.5 }}>
          No {cropWord} variety has enough side-by-side evidence to call yet.
        </div>
      )}

      {thin.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>Not enough to call yet</div>
          <div style={{ fontSize: 11.5, color: T.faint, marginBottom: 4 }}>Fewer than {YF_VAR_MIN_FIELDS} fields or {YF_VAR_MIN_SEASONS} seasons side by side, or results too scattered to read. Listed so nothing is hidden; not ranked, and in no order of merit.</div>
          {(allThin ? thin : thin.slice(0, 10)).map(v => (
            <div key={v.name} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "3px 0", borderBottom: `1px solid ${T.lineSoft}`, fontSize: 12, color: T.body }}>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><b style={{ color: T.ink }}>{v.name}</b></span>
              <span style={{ whiteSpace: "nowrap", color: T.faint }}>{v.n} field{v.n === 1 ? "" : "s"}, {v.seasons} season{v.seasons === 1 ? "" : "s"}{v.thinWhy === "results too scattered" ? ", too scattered" : ""}</span>
            </div>
          ))}
          {thin.length > 10 && (
            <button onClick={() => setAllThin(o => !o)} style={{ cursor: "pointer", font: "inherit", fontSize: 12, border: "none", background: "none", padding: "5px 0 0", color: T.green, fontWeight: 700 }}>
              {allThin ? "Show fewer" : `Show all ${thin.length}`}
            </button>
          )}
        </div>
      )}

      <div style={{ marginTop: 12, paddingTop: 9, borderTop: `1px solid ${T.lineSoft}`, fontSize: 11.5, color: T.body, lineHeight: 1.55 }}>
        <b style={{ color: T.ink }}>{ac(ev.used)} of {ac(ev.onFile)} ac used.</b>
        {leftParts.length > 0 && <> Left out: {leftParts.join("; ")}.</>}
        {" "}
        <button onClick={() => setHowOpen(o => !o)} style={{ cursor: "pointer", font: "inherit", border: "none", background: "none", padding: 0, color: T.green, fontWeight: 700 }}>
          {howOpen ? "Hide how this is worked out" : "How this is worked out"}
        </button>
      </div>
      {howOpen && (
        <ol style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 11.5, color: T.body, lineHeight: 1.55 }}>
          <li>Only clean harvests: one variety on the field, the planter and the combine agree on it, moisture in the normal range for {cropWord}, and the pass covered the field.</li>
          <li>Only side by side: a variety is compared with the other varieties on the same farm in the same season, so weather, operator and combine are the same. Never across crops; silage and grain are kept apart.</li>
          <li>Ground taken out: each field{"’"}s yield is divided by how that field did against the farm in its other seasons of {cropWord}, never the season being judged. A field needs {YF_VAR_MIN_HISTORY} other seasons to count, and a short record is trusted less.</li>
          <li>The number is the acre-weighted average of those field results. The range is where the true difference likely sits (90% confidence), using whichever of the field-to-field or season-to-season spread is wider.</li>
          <li>{"“"}Ahead{"”"} or {"“"}Behind{"”"} only when the whole range is on one side of even. {YF_VAR_MIN_FIELDS}+ fields over {YF_VAR_MIN_SEASONS}+ seasons are needed before a variety is called at all.</li>
          <li>This is not a strip trial. Almost no field in the John Deere data has two varieties harvested side by side in it, so this is the closest fair comparison the records allow. Spellings of one product are merged ({"“"}DKC49-72{"”"}, {"“"}49-72 Corn{"”"} and {"“"}49-72{"”"}); seed treatments and add-ons such as HeadsUp, Pivot Bio or RIB stay separate.</li>
        </ol>
      )}
    </div>
  );
}

// Planting weeks, keyed so they sort as strings and label as a date range. Weeks
// rather than days because a per-day chart on 200 fields is noise, and rather than
// months because the whole question is a 10-day window in May.
function yfWeekKey(iso) {
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d)) return "\u0000none";
  const day = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - day);          // back to Sunday
  return d.toISOString().slice(0, 10);
}
function yfWeekLabel(key) {
  const d = new Date(key + "T00:00:00Z");
  if (isNaN(d)) return key;
  const end = new Date(d); end.setUTCDate(end.getUTCDate() + 6);
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const a = `${M[d.getUTCMonth()]} ${d.getUTCDate()}`;
  const b = d.getUTCMonth() === end.getUTCMonth() ? `${end.getUTCDate()}` : `${M[end.getUTCMonth()]} ${end.getUTCDate()}`;
  return `${a}–${b}, ${d.getUTCFullYear()}`;
}

// Year-agnostic planting windows, for when more than one season is in view.
// Day-of-month, NOT ISO week: an ISO week key carries its year, so the same
// window in two seasons lands in two bars and neither can be compared. Four
// bins a month because the whole question is a 10-day window in May, and a
// month-wide bar hides it.
const YF_WIN = [[1, 7, "1–7"], [8, 14, "8–14"], [15, 21, "15–21"], [22, 31, "22–end"]];
function yfWindowKey(iso) {
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d)) return "\u0000none";
  const day = d.getUTCDate();
  const w = YF_WIN.findIndex(([a, b]) => day >= a && day <= b);
  return String(d.getUTCMonth()).padStart(2, "0") + "-" + (w < 0 ? 3 : w);
}
function yfWindowLabel(key) {
  const p = key.split("-");
  const m = Number(p[0]), w = Number(p[1]);
  const M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (!M[m] || !YF_WIN[w]) return key;
  return M[m] + "\n" + YF_WIN[w][2];
}

// ─────────────────────────────────────────────────────────────────────────────
// YIELD ANALYTICS — THE REDESIGN (Kevin, 2026-11-01: "The yield analytics redesign
// is fantastic, build it all"). The page opens on how THIS SEASON went against the
// farm's own history, then shows the ground two ways — on the real field map, and
// as good ground vs. dependable ground — and every way in (a field on the map, a
// dot on the chart, a row in the list) opens the same field card.
//
// No new numbers are invented here. The index, rank, stability and quadrant are the
// server's (hYieldAnalytics); the season figures are its operation means, served as
// `seasons`. Silage and grain are separate crop keys end to end and never share an
// average, a colour scale or a chart (Kevin: "we never merge those").
// ─────────────────────────────────────────────────────────────────────────────

// How a crop key reads on this page. CORN here is only ever combine data (silage is
// its own key), so it says "Corn grain" beside "Corn silage" rather than "Corn".
const yaCropLabel = (c) => (c === YA_ALL ? "All crops"
  : String(c || "").toUpperCase() === "CORN" ? "Corn grain" : fmCropName(c));
// A crop in the few letters a chart label has room for. Silage gets its own word —
// slicing "CORN SILAGE" to four letters printed "CORN" on a chopped year.
const yaCropShort = (c) => String(c || "").split(" + ").map(x => (jdIsSilage(x) ? "Silage" : yaCropLabel(x).split(" ")[0].slice(0, 5))).join("+");
const yaIsCornFamily = (c) => /^CORN( SILAGE|_SILAGE)?$/i.test(String(c || ""));

// One diverging scale for "how this ground does against the rest of the farm", used
// by the map and the chart alike so the two read as one picture. The bands are the
// index itself — 100% is the farm's own average — never a fixed yield.
const YA_IX_BANDS = [
  { lo: 1.10,  color: "#2f7d32", label: "110%+" },
  { lo: 1.03,  color: "#8fbf72", label: "103–110%" },
  { lo: 0.97,  color: "#ece6c2", label: "About average" },
  { lo: 0.90,  color: "#e2a497", label: "90–97%" },
  { lo: -1,    color: "#b5473f", label: "Below 90%" },
];
const yaIxColor = (ix) => (ix == null ? "#c9d3c6" : YA_IX_BANDS.find(b => ix >= b.lo).color);
const yaPct = (ix) => `${Math.round(ix * 100)}%`;
const yaFmtYld = (v, crop) => (v == null ? "—" : jdIsSilage(crop) ? Number(v).toFixed(1) : Math.round(v).toLocaleString("en-US"));

// The farm average for one crop, year by year, over whichever farms are in view.
// One farm: exactly that farm's operation mean. Several: acre-weighted across them,
// which is the same as pooling every field-year — and the header says it spans farms.
function yaSeasonSeries(seasons, crop, farmId) {
  const by = new Map();
  for (const s of seasons || []) {
    if (s.crop !== crop || (farmId && s.farmer_id !== farmId)) continue;
    // The same floor the index uses for an honest operation mean: a "farm average"
    // struck over one or two fields is those fields, not the farm.
    if (!(s.n >= YA_MIN_PEERS)) continue;
    const c = by.get(s.yr) || { yr: s.yr, ac: 0, sum: 0, n: 0, apprAc: 0, farms: new Set() };
    c.ac += s.ac; c.sum += s.mean * s.ac; c.n += s.n; c.apprAc += Number(s.apprAc) || 0; c.farms.add(s.farmer_id);
    by.set(s.yr, c);
  }
  return [...by.values()].filter(c => c.ac > 0)
    .map(c => ({ yr: c.yr, ac: c.ac, n: c.n, farms: c.farms.size, mean: c.sum / c.ac, apprAc: c.apprAc }))
    .sort((a, b) => a.yr - b.yr);
}
// The latest season against the farm's own earlier seasons. Each earlier season counts
// once — a big-acre year does not outvote a small one in "what is normal here".
function yaSeasonRead(series) {
  if (!series.length) return null;
  const last = series[series.length - 1];
  const prior = series.slice(0, -1);
  const hist = prior.length ? prior.reduce((s, y) => s + y.mean, 0) / prior.length : null;
  return { last, prior, hist, delta: hist ? last.mean / hist - 1 : null,
           histSpan: !prior.length ? null : prior.length === 1 ? `${prior[0].yr}` : `${prior[0].yr}–${prior[prior.length - 1].yr}` };
}

// ── SEASON HEADER ───────────────────────────────────────────────────────────
// THE APH ON THE POLICY, for one farm and one crop key. The server has already
// acre-weighted the base lines (overlays and untyped corn left out) — this only picks
// the policy year that matches the season, else the latest on file. An APH is a fact
// of ONE farm's policy, so it is never shown when several farms are in view.
function yaAphFor(aph, farmId, crop, yr) {
  if (!farmId) return null;
  const mine = (aph || []).filter(a => a.farmer_id === farmId && a.crop === String(crop || "").toUpperCase() && a.aph > 0);
  if (!mine.length) return null;
  return mine.find(a => a.crop_year === yr) || mine.sort((a, b) => b.crop_year - a.crop_year)[0];
}

function YaSeasonHeader({ seasons, aph, aphFarmId, crop, farmId, cropOptions, onPickCrop, nFarmsInView, isMobile }) {
  const card = { background: "#fff", border: `1px solid ${T.lineStrong}`, borderRadius: 16, padding: isMobile ? "12px 14px" : "14px 18px", marginBottom: 12 };
  const lbl = { fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em", color: "#4f6a4c", textTransform: "uppercase" };
  const deltaText = (d) => (d == null ? null : Math.abs(d) < 0.005 ? "right on" : `${Math.abs(Math.round(d * 100))}% ${d > 0 ? "above" : "below"}`);
  const deltaColor = (d) => (d == null || Math.abs(d) < 0.03 ? T.body : d > 0 ? "#2f7d32" : "#a8453c");
  const spanNote = nFarmsInView > 1 ? ` · across ${nFarmsInView} farms in view` : "";

  if (crop === YA_ALL) {
    // Every crop's season in one row of tiles. Each tile is its own crop in its own
    // unit — the tiles sit side by side, they are never summed.
    const tiles = cropOptions.filter(c => c !== YA_ALL).map(c => {
      const rd = yaSeasonRead(yaSeasonSeries(seasons, c, farmId));
      return rd ? { c, rd } : null;
    }).filter(Boolean);
    if (!tiles.length) return null;
    return (
      <div style={card}>
        <div style={lbl}>Latest season, every crop{spanNote}</div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 140 : 180}px, 1fr))`, gap: 8, marginTop: 10 }}>
          {tiles.map(({ c, rd }) => (
            <button key={c} onClick={() => onPickCrop(c)}
              style={{ textAlign: "left", font: "inherit", cursor: "pointer", background: "#f7faf7",
                       border: `1px solid ${T.line}`, borderRadius: 12, padding: "10px 12px", minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>{yaCropLabel(c)} <span style={{ color: T.faint, fontWeight: 600 }}>{rd.last.yr}</span></div>
              <div style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 700, color: T.ink, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                {yaFmtYld(rd.last.mean, c)} <span style={{ fontFamily: T.sans, fontSize: 11.5, fontWeight: 700, color: T.faint }}>{yaUnit(c)}/ac</span>
              </div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: deltaColor(rd.delta) }}>
                {rd.delta == null ? "First season on file" : `${deltaText(rd.delta)} your ${rd.histSpan} average`}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const series = yaSeasonSeries(seasons, crop, farmId);
  const rd = yaSeasonRead(series);
  if (!rd) return null;
  const unit = `${yaUnit(crop)}/ac`;
  const pol = yaAphFor(aph, aphFarmId, crop, rd.last.yr);
  const vsAph = pol && pol.crop_year === rd.last.yr && pol.aph > 0 ? rd.last.mean / pol.aph - 1 : null;
  const max = Math.max(...series.map(s => s.mean), rd.hist || 0, pol ? pol.aph : 0) * 1.08;
  const barH = isMobile ? 64 : 76;

  // HOW THE CORN CAME OFF. Corn grain and corn silage are separate crops on this page
  // and keep separate yields, but the acres of one season are one crop in the ground
  // (Kevin, 2026-10-31: "show corn and then show how the acres were harvested").
  // Appraised acres are adjuster appraisals on fields no machine harvested; they sit in
  // corn grain (Kevin: "silage appraisals actually will be bushels") and are shown apart
  // from the combine's acres, never folded into them.
  let split = null;
  if (yaIsCornFamily(crop)) {
    const keys = cropOptions.filter(c => c !== YA_ALL && yaIsCornFamily(c));
    const at = (c, k) => { const s = yaSeasonSeries(seasons, c, farmId).find(x => x.yr === rd.last.yr); return s ? s[k] || 0 : 0; };
    const chopped = keys.filter(jdIsSilage).reduce((s, c) => s + at(c, "ac"), 0);
    const appraised = keys.filter(c => !jdIsSilage(c)).reduce((s, c) => s + at(c, "apprAc"), 0);
    const combined = keys.filter(c => !jdIsSilage(c)).reduce((s, c) => s + at(c, "ac"), 0) - appraised;
    if (chopped + combined + appraised > 0) split = { chopped, combined, appraised };
  }
  const apprLast = rd.last.apprAc > 0 ? rd.last.apprAc : 0;

  return (
    <div style={card}>
      <div style={lbl}>{rd.last.yr} season · {yaCropLabel(crop)}{spanNote}</div>
      <div style={{ display: "flex", gap: isMobile ? 12 : 22, alignItems: "flex-end", flexWrap: "wrap", marginTop: 6 }}>
        <div style={{ minWidth: 0, flex: "1 1 220px" }}>
          <div style={{ fontFamily: T.serif, fontSize: isMobile ? 32 : 38, fontWeight: 700, color: T.ink, lineHeight: 1.05, fontVariantNumeric: "tabular-nums" }}>
            {yaFmtYld(rd.last.mean, crop)}<span style={{ fontFamily: T.sans, fontSize: 15, fontWeight: 700, color: T.faint, marginLeft: 6 }}>{unit}</span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: deltaColor(rd.delta), marginTop: 4 }}>
            {rd.delta == null
              ? "First season on file — the comparison starts next year"
              : <>{deltaText(rd.delta)} your {rd.histSpan} average of {yaFmtYld(rd.hist, crop)} {unit}
                  <span style={{ color: T.faint, fontWeight: 600 }}> ({rd.prior.length} season{rd.prior.length === 1 ? "" : "s"}, each counted once)</span></>}
          </div>
          {pol && (
            <div style={{ fontSize: 13, fontWeight: 700, color: vsAph == null ? T.body : deltaColor(vsAph), marginTop: 3 }}>
              {vsAph == null
                ? <>APH on your {pol.crop_year} policy: {yaFmtYld(pol.aph, crop)} {unit}</>
                : <>{deltaText(vsAph)} your {pol.crop_year} APH of {yaFmtYld(pol.aph, crop)} {unit}</>}
              {pol.max > pol.min && (
                <span style={{ color: T.faint, fontWeight: 600 }}> (lines run {yaFmtYld(pol.min, crop)}–{yaFmtYld(pol.max, crop)}, weighted by acres)</span>
              )}
              {vsAph != null && jdIsSilage(crop) && (
                <div style={{ fontSize: 11.5, color: T.faint, fontWeight: 600, marginTop: 2 }}>
                  Machine tons are wet tons at 35% dry matter (65% moisture).
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize: 12, color: T.faint, marginTop: 3 }}>
            {Math.round(rd.last.ac).toLocaleString("en-US")} ac harvested · {rd.last.n} field{rd.last.n === 1 ? "" : "s"} · {!apprLast ? "from John Deere machine data"
              : apprLast >= rd.last.ac - 0.5 ? "all from adjuster appraisals, on fields no machine harvested"
              : <>{Math.round(rd.last.ac - apprLast).toLocaleString("en-US")} ac from John Deere machine data, {Math.round(apprLast).toLocaleString("en-US")} ac from adjuster appraisals on fields no machine harvested</>}
          </div>
          {split && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {[["Chopped", split.chopped, "#b58f3a"], ["Combined", split.combined, "#2d6a2d"], ["Appraised", split.appraised, "#2f5f8a"]]
                .filter(([w, ac]) => w !== "Appraised" || ac > 0).map(([w, ac, col]) => (
                <span key={w} style={{ fontSize: 12, fontWeight: 700, color: col, background: "#f7faf7", border: `1px solid ${T.line}`, borderRadius: 999, padding: "3px 10px" }}>
                  {w} {Math.round(ac).toLocaleString("en-US")} ac
                </span>
              ))}
              <span style={{ fontSize: 11.5, color: T.faint, alignSelf: "center" }}>{rd.last.yr} corn</span>
            </div>
          )}
        </div>
        {/* Every season on file, this one dark. The dashed line is the average of the
            earlier seasons — the yardstick the sentence above is read against. */}
        <div style={{ flex: "1 1 260px", minWidth: 0 }}>
          <div style={{ position: "relative", height: barH, display: "flex", alignItems: "flex-end", gap: 4 }}>
            {series.map(s => {
              const on = s.yr === rd.last.yr;
              return (
                <div key={s.yr} title={`${s.yr}: ${yaFmtYld(s.mean, crop)} ${unit} on ${Math.round(s.ac)} ac`}
                     style={{ flex: "1 1 0", minWidth: 0, height: `${Math.max(4, (s.mean / max) * barH)}px`,
                              background: on ? "#2d6a2d" : "#cfe0cb", borderRadius: "4px 4px 0 0" }} />
              );
            })}
            {rd.hist != null && (
              <div style={{ position: "absolute", left: 0, right: 0, bottom: `${(rd.hist / max) * barH}px`, borderTop: "2px dashed #8a5e10" }} />
            )}
            {pol && (
              <div style={{ position: "absolute", left: 0, right: 0, bottom: `${(pol.aph / max) * barH}px`, borderTop: "2px dashed #2f5f8a" }} />
            )}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
            {series.map(s => (
              <div key={s.yr} style={{ flex: "1 1 0", minWidth: 0, textAlign: "center", fontSize: 10.5, color: s.yr === rd.last.yr ? T.ink : T.faint, fontWeight: s.yr === rd.last.yr ? 800 : 600 }}>
                {series.length > 8 ? `'${String(s.yr).slice(2)}` : s.yr}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 5, fontSize: 11, color: T.faint }}>
            {rd.hist != null && <span><span style={{ display: "inline-block", width: 14, borderTop: "2px dashed #8a5e10", verticalAlign: "middle", marginRight: 4 }} />earlier seasons</span>}
            {pol && <span><span style={{ display: "inline-block", width: 14, borderTop: "2px dashed #2f5f8a", verticalAlign: "middle", marginRight: 4 }} />{pol.crop_year} APH</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── GROUND MAP ──────────────────────────────────────────────────────────────
// The farm's real John Deere field outlines, each painted by its multi-year index in
// the crop on screen. ?farmer= is the scope, not a convenience: a preview-as session
// holds an ADMIN token, and the server intersects the id with what the caller holds.
const YA_MAP_CACHE = new Map();
// Leaflet's own fix for tile seams (2.0): adjacent tiles add, so the half-covered pixel
// on a shared edge comes out whole instead of letting the background show through.
const YA_MAP_CSS = ".ya-base img.leaflet-tile{mix-blend-mode:plus-lighter}";

function YaGroundMap({ farmerId, scored, cropLabel, onPick, focusKey, isMobile }) {
  const divRef = useRef(null), mapRef = useRef(null), layerRef = useRef(null), fitRef = useRef(null);
  const [gj, setGj] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true; setGj(null); setErr(null);
    // One read per farm, kept five minutes, so flipping crops or tabs does not ask again.
    const hit = YA_MAP_CACHE.get(farmerId);
    const p = (hit && Date.now() - hit.t < 5 * 60 * 1000) ? hit.p
      : authedFetch(`/api/market?fn=jd-fieldmap&farmer=${encodeURIComponent(farmerId)}`)
          .then(r => r.json())
          .then(g => (g && g.features ? g : { type: "FeatureCollection", features: [] }));
    if (p !== (hit && hit.p)) YA_MAP_CACHE.set(farmerId, { t: Date.now(), p });
    p.then(g => { if (alive) setGj(g); })
     .catch(e => { YA_MAP_CACHE.delete(farmerId); if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [farmerId]);
  useEffect(() => () => { if (mapRef.current) { try { mapRef.current.remove(); } catch (e) {} mapRef.current = null; } }, []);

  const byId = useMemo(() => {
    const m = new Map();
    for (const r of scored) if (r.farmer_id === farmerId && r.jd_field_id != null) m.set(String(r.jd_field_id), r);
    return m;
  }, [scored, farmerId]);
  const drawn = useMemo(() => (gj ? gj.features.filter(f => f.geometry && byId.has(String((f.properties || {}).jd_field_id))).length : 0), [gj, byId]);

  useEffect(() => {
    if (!gj || !divRef.current) return;
    let alive = true;
    loadLeaflet().then(L => {
      if (!alive || !divRef.current) return;
      let map = mapRef.current;
      if (!map) {
        map = L.map(divRef.current, { zoomControl: true, attributionControl: false, scrollWheelZoom: false }).setView([43, -77.5], 8);
        // ya-base: the imagery tiles get the seam fix in YA_MAP_CSS. Safari leaves a hairline
        // gap between tiles at some offsets, which drew as a dark line across the farm.
        L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 20, className: "ya-base" }).addTo(map);
        L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}", { maxZoom: 20, opacity: 0.8 }).addTo(map);
        mapRef.current = map;
      }
      try { map.invalidateSize(); } catch (e) {}
      if (layerRef.current) { try { map.removeLayer(layerRef.current); } catch (e) {} layerRef.current = null; }
      const feats = gj.features.filter(f => f.geometry);
      if (!feats.length) return;
      layerRef.current = L.geoJSON({ type: "FeatureCollection", features: feats }, {
        style: (f) => {
          const r = byId.get(String((f.properties || {}).jd_field_id));
          return r ? { color: "#ffffff", weight: 1.3, opacity: 0.95, fill: true, fillColor: yaIxColor(r.index), fillOpacity: 0.78 }
                   : { color: "#ffffff", weight: 0.9, opacity: 0.55, dashArray: "3 3", fill: false };
        },
        onEachFeature: (f, lyr) => {
          const p = f.properties || {};
          const r = byId.get(String(p.jd_field_id));
          const nm = String((r && r.field) || p.field || "Field").replace(/[<>&]/g, "");
          lyr.bindTooltip(r ? `<b>${nm}</b><br>${yaPct(r.index)} of your ${cropLabel.toLowerCase()} average · #${r.rank} of ${r.of}`
                            : `<b>${nm}</b><br>Not scored in ${cropLabel.toLowerCase()}`, { sticky: true, direction: "top" });
          if (r) lyr.on("click", () => onPick(r.key));
        },
      }).addTo(map);
      // Fit once per farm, not on every crop switch, so flipping crops compares the
      // same view instead of jumping around.
      if (fitRef.current !== farmerId) {
        const scoredLayers = layerRef.current.getLayers().filter(l => byId.has(String(((l.feature || {}).properties || {}).jd_field_id)));
        const b = (scoredLayers.length ? L.featureGroup(scoredLayers) : layerRef.current).getBounds();
        if (b && b.isValid()) { map.fitBounds(b, { padding: [16, 16] }); fitRef.current = farmerId; }
      }
      setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 80);
    }).catch(() => { if (alive) setErr("map could not load"); });
    return () => { alive = false; };
  }, [gj, byId, cropLabel, farmerId]); // eslint-disable-line

  // A tapped field is outlined and brought into view (Kevin, 2026-10-31: tapping a
  // field should zoom to it). Opened from the list or the chart, the map follows too.
  useEffect(() => {
    const map = mapRef.current, lyr = layerRef.current;
    if (!map || !lyr) return;
    const row = focusKey ? scored.find(r => r.key === focusKey) : null;
    lyr.eachLayer(l => {
      const id = String(((l.feature || {}).properties || {}).jd_field_id);
      const r = byId.get(id);
      const on = row && r && r.key === row.key;
      if (r) l.setStyle({ color: on ? "#ffe600" : "#ffffff", weight: on ? 3.5 : 1.3 });
      if (on) { try { map.fitBounds(l.getBounds(), { padding: [40, 40], maxZoom: 16 }); } catch (e) {} }
    });
  }, [focusKey, gj, byId]); // eslint-disable-line

  return (
    <div style={{ background: "#fff", border: `1px solid ${T.lineStrong}`, borderRadius: 16, overflow: "hidden", marginBottom: 12 }}>
      <div style={{ padding: "12px 14px 8px" }}>
        <div style={{ fontFamily: T.serif, fontSize: 18, fontWeight: 700, color: T.ink }}>Your ground, best to weakest</div>
        <div style={{ fontSize: 12.5, color: "#4f6a4c", marginTop: 2 }}>
          Each field colored by how {cropLabel === "All crops" ? "it" : `its ${cropLabel.toLowerCase()}`} does against the rest of your farm in the same year, over every year on file. Tap a field for its story.
        </div>
      </div>
      <div style={{ position: "relative" }}>
        <style>{YA_MAP_CSS}</style>
        {/* Black behind the imagery: plus-lighter adds each tile onto what is under it. */}
        <div ref={divRef} style={{ height: isMobile ? 330 : 430, background: "#000" }} />
        {(!gj || err || (gj && !drawn)) && (
          // Above Leaflet's panes (z 400) and controls (z 1000), or the message is hidden under the map.
          <div style={{ position: "absolute", inset: 0, zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
                        color: "#e9efe6", fontSize: 13, textAlign: "center", background: gj && !err ? "rgba(20,30,18,0.55)" : "transparent", pointerEvents: "none" }}>
            {err ? `Couldn't load the field map (${err}).`
              : !gj ? "Loading your field map…"
              : `No John Deere field outline matches a scored ${cropLabel.toLowerCase()} field yet — the list below has every one.`}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", padding: "10px 14px", alignItems: "center" }}>
        {YA_IX_BANDS.slice().reverse().map(b => (
          <span key={b.label} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.body }}>
            <span style={{ width: 14, height: 10, borderRadius: 3, background: b.color, border: "1px solid rgba(0,0,0,0.12)" }} />{b.label}
          </span>
        ))}
        <span style={{ fontSize: 11.5, color: T.faint }}>· dashed = not scored in this crop</span>
      </div>
    </div>
  );
}

// ── YOUR GROUND AS BLOCKS ──────────────────────────────────────────────────
// Kevin, 2026-11-01: "I like the map idea and the block idea, I would do both." Every
// ranked field of the crop in view is one tile, sized by acres and colored on the same
// scale as the map, laid out best (top left) to weakest (bottom right). Rows are cut in
// rank order and each row is as tall as its share of the acres, so a tile's AREA is its
// acres. Corn silage and corn grain are separate crop tabs, so they never share a tile.
function YaFieldTiles({ scored, cropLabel, onPick, openKey, isMobile }) {
  const list = scored.filter(r => r.index != null && r.acres > 0).slice().sort((a, b) => b.index - a.index);
  if (!list.length) return null;
  const tot = list.reduce((s, r) => s + r.acres, 0);
  const W = 680, H = Math.min(isMobile ? 420 : 380, Math.max(120, 44 * Math.sqrt(list.length) + 60));
  const nRows = Math.max(1, Math.min(8, Math.round(Math.sqrt(list.length / 2))));
  const per = Math.ceil(list.length / nRows);
  const tiles = [];
  let y = 0;
  for (let i = 0; i < nRows; i++) {
    const chunk = list.slice(i * per, (i + 1) * per);
    if (!chunk.length) break;
    const ra = chunk.reduce((s, r) => s + r.acres, 0);
    const h = H * ra / tot;
    let x = 0;
    for (const r of chunk) { const w = W * r.acres / ra; tiles.push({ r, x, y, w, h }); x += w; }
    y += h;
  }
  const dark = (ix) => ix != null && (ix >= 1.10 || ix < 0.90);
  return (
    <div style={{ background: "#fff", border: `1px solid ${T.lineStrong}`, borderRadius: 16, padding: "12px 14px", marginBottom: 12 }}>
      <div style={{ fontFamily: T.serif, fontSize: 18, fontWeight: 700, color: T.ink }}>Your ground as blocks</div>
      <div style={{ fontSize: 12.5, color: "#4f6a4c", marginTop: 2, marginBottom: 8 }}>
        Each block is a field, sized by acres, best at the top left to weakest at the bottom right. Colored like the map. Tap one for its story.
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img"
           aria-label={`${list.length} ${cropLabel.toLowerCase()} fields as blocks sized by acres, best to weakest`}>
        {tiles.map(({ r, x, y: ty, w, h }) => {
          const on = r.key === openKey;
          const fs = isMobile ? 15 : 12;
          return (
            <g key={r.key} onClick={() => onPick(r.key)} style={{ cursor: "pointer" }}>
              <rect x={x + 1} y={ty + 1} width={Math.max(w - 2, 1)} height={Math.max(h - 2, 1)} rx={4}
                    fill={yaIxColor(r.index)} stroke={on ? "#111" : "none"} strokeWidth={on ? 3 : 0} />
              {w > fs * 3 && h > fs * 1.6 && (
                <text x={x + 6} y={ty + fs + 4} fontSize={fs} fontWeight="700" fill={dark(r.index) ? "#fff" : "#1f2d1c"}
                      style={{ pointerEvents: "none" }}>
                  {String(r.field).length * fs * 0.6 > w - 10 ? String(r.field).slice(0, Math.max(1, Math.floor((w - 10) / (fs * 0.6)))) : r.field}
                </text>
              )}
              <title>{`${r.field} · ${yaPct(r.index)} of your average · ${Math.round(r.acres)} ac${r.rank ? ` · #${r.rank} of ${r.of}` : ""}`}</title>
            </g>
          );
        })}
      </svg>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", marginTop: 8, alignItems: "center" }}>
        {YA_IX_BANDS.slice().reverse().map(b => (
          <span key={b.label} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.body }}>
            <span style={{ width: 14, height: 10, borderRadius: 3, background: b.color, border: "1px solid rgba(0,0,0,0.12)" }} />{b.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── GOOD GROUND VS. DEPENDABLE GROUND ───────────────────────────────────────
// Right is better than the farm's average; up swings more year to year once the
// season is divided out. The horizontal line is the stable/unstable cut the boxes
// use (YA_HIGH_CV), so a dot's corner IS its box. Ground short of a third year has
// no swing to plot, so it sits in its own strip under the chart instead of being
// drawn at a made-up height.
function YaSteadinessChart({ scored, onPick, openKey, cropLabel, isMobile }) {
  const rated = scored.filter(r => r.icv != null);
  const unrated = scored.filter(r => r.icv == null);
  // An empty quadrant grid over four "0 ac" boxes read as broken (Sullivan soybeans,
  // history from 2024: no field has three soybean years yet). Say what is missing.
  if (!rated.length) {
    const ac = Math.round(unrated.reduce((s, r) => s + (r.acres || 0), 0));
    const crop = cropLabel === "All crops" ? "" : cropLabel.toLowerCase() + " ";
    return (
      <div style={{ background: "#fff", border: `1px solid ${T.lineStrong}`, borderRadius: 16, padding: "14px 16px", marginBottom: 12 }}>
        <div style={{ fontFamily: T.serif, fontSize: 18, fontWeight: 700, color: T.ink }}>Good ground vs. dependable ground</div>
        <div style={{ fontSize: 13, color: T.body, marginTop: 6, lineHeight: 1.6 }}>
          Steadiness needs {YA_MIN_CV_YEARS} years of {crop || "the same crop "}on a field, and no field has that yet.
          {unrated.length > 0 && <> {unrated.length} field{unrated.length === 1 ? " is" : "s are"} ranked ({ac.toLocaleString()} ac) on fewer years. Tap one below for its story.</>}
          {" "}This view fills in as more seasons come in.
        </div>
        {unrated.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
            {unrated.slice().sort((a, b) => b.index - a.index).map(r => (
              <button key={r.key} onClick={() => onPick(r.key)}
                style={{ cursor: "pointer", font: "inherit", fontSize: 12, fontWeight: 700, padding: "5px 10px", borderRadius: 999,
                         border: `1px solid ${r.key === openKey ? "#111" : T.line}`, background: "#f7faf7", color: T.ink }}>
                {r.field} · {yaPct(r.index)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  const W = 680, H = isMobile ? 330 : 360, padL = 46, padR = 14, padT = 14, stripH = unrated.length ? 34 : 0, padB = 34 + stripH;
  const ixs = scored.map(r => r.index);
  const xLo = Math.max(0.5, Math.min(0.8, ...ixs.map(x => x - 0.03)));
  const xHi = Math.min(1.6, Math.max(1.2, ...ixs.map(x => x + 0.03)));
  const yHi = Math.min(0.6, Math.max(0.3, ...rated.map(r => r.icv + 0.03)));
  const px = (x) => padL + (x - xLo) / (xHi - xLo) * (W - padL - padR);
  const py = (y) => padT + (1 - y / yHi) * (H - padT - padB);
  const rr = (ac) => Math.max(3.5, Math.min(15, 2.5 + Math.sqrt(ac || 0) * 0.75));
  const col = (r) => (r.quad ? YA_QUAD[r.quad].color : YA_UNRATED.color);
  const xTicks = []; for (let t = Math.ceil(xLo * 10) / 10; t <= xHi + 1e-9; t += 0.1) xTicks.push(+t.toFixed(2));
  const yTicks = []; for (let t = 0; t <= yHi + 1e-9; t += 0.1) yTicks.push(+t.toFixed(2));
  const plotBottom = H - padB;
  const tip = (r) => `${r.field} · ${yaPct(r.index)} of your average${r.icv != null ? ` · swings ±${Math.round(r.icv * 100)}%` : " · not enough years for a swing"} · ${Math.round(r.acres)} ac`;
  const dot = (r, cx, cy, hollow) => {
    const on = r.key === openKey;
    return (
      <circle key={r.key} cx={cx} cy={cy} r={rr(r.acres)} onClick={() => onPick(r.key)}
        style={{ cursor: "pointer" }} fill={hollow ? "#fff" : col(r)} fillOpacity={hollow ? 1 : 0.78}
        stroke={on ? "#111" : hollow ? col(r) : "#fff"} strokeWidth={on ? 2.5 : 1.2}>
        <title>{tip(r)}</title>
      </circle>
    );
  };
  const corner = (k, x, y, anchor) => (
    <text x={x} y={y} textAnchor={anchor} fontSize="11.5" fontWeight="800" fill={YA_QUAD[k].color}>{YA_QUAD[k].label}</text>
  );
  return (
    <div style={{ background: "#fff", border: `1px solid ${T.lineStrong}`, borderRadius: 16, padding: "12px 14px", marginBottom: 12 }}>
      <div style={{ fontFamily: T.serif, fontSize: 18, fontWeight: 700, color: T.ink }}>Good ground vs. dependable ground</div>
      <div style={{ fontSize: 12.5, color: "#4f6a4c", marginTop: 2, marginBottom: 8 }}>
        Right is better than your {cropLabel === "All crops" ? "" : cropLabel.toLowerCase() + " "}average. Up swings more year to year. Dot size is acres. Tap a dot for that field.
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img"
           aria-label={`Scatter of ${scored.length} fields by performance and year-to-year swing`}>
        {yTicks.map(t => (
          <g key={`y${t}`}>
            <line x1={padL} x2={W - padR} y1={py(t)} y2={py(t)} stroke="#eef2ee" />
            <text x={padL - 6} y={py(t) + 4} textAnchor="end" fontSize="11" fill={T.faint}>±{Math.round(t * 100)}%</text>
          </g>
        ))}
        {xTicks.map(t => (
          <text key={`x${t}`} x={px(t)} y={plotBottom + 16} textAnchor="middle" fontSize="11" fill={T.faint}>{Math.round(t * 100)}%</text>
        ))}
        <line x1={px(1)} x2={px(1)} y1={padT} y2={plotBottom} stroke="#5d6b5b" strokeDasharray="4 4" />
        <line x1={padL} x2={W - padR} y1={py(YA_HIGH_CV)} y2={py(YA_HIGH_CV)} stroke="#5d6b5b" strokeDasharray="4 4" />
        <text x={px(1) + 4} y={padT + 10} fontSize="10.5" fill="#5d6b5b">your average</text>
        <text x={W - padR - 2} y={py(YA_HIGH_CV) - 5} textAnchor="end" fontSize="10.5" fill="#5d6b5b">±{Math.round(YA_HIGH_CV * 100)}% swing</text>
        {corner("boom", W - padR - 2, padT + 26, "end")}
        {corner("fragile", padL + 4, padT + 26, "start")}
        {corner("anchor", W - padR - 2, plotBottom - 8, "end")}
        {corner("structural", padL + 4, plotBottom - 8, "start")}
        {rated.slice().sort((a, b) => b.acres - a.acres).map(r => dot(r, px(r.index), py(Math.min(r.icv, yHi)), false))}
        {unrated.length > 0 && (
          <g>
            <line x1={padL} x2={W - padR} y1={H - stripH + 2} y2={H - stripH + 2} stroke="#eef2ee" />
            <text x={padL - 6} y={H - stripH / 2 + 8} textAnchor="end" fontSize="10.5" fill={YA_UNRATED.color}>new</text>
            {unrated.slice().sort((a, b) => b.acres - a.acres).map(r => dot(r, px(r.index), H - stripH / 2 + 4, true))}
          </g>
        )}
      </svg>
      {unrated.length > 0 && (
        <div style={{ fontSize: 11.5, color: T.faint, marginTop: 4 }}>
          Hollow dots along the bottom: {unrated.length} field{unrated.length === 1 ? "" : "s"} with fewer than {YA_MIN_CV_YEARS} years of this crop. Ranked, but no swing yet.
        </div>
      )}
    </div>
  );
}

// ── ONE FIELD'S STORY ───────────────────────────────────────────────────────
// The same detail whether the field was opened from the map, the chart or the list.
function YaFieldDetail({ r, crop, cropLabel, isAll, unit }) {
  const q = r.quad ? YA_QUAD[r.quad] : null;
  const st = yaSteadyWord(r.icv);
  return (
    <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 300px" }}>
        <div style={{ ...uiLabel, marginBottom: 6 }}>
          {isAll ? "EVERY YEAR" : `${cropLabel} YEAR BY YEAR`}
        </div>
        {/* Only this crop's years. The bar is the index, the
            number under it is the actual bushels — the two
            together are why a 190 bu year can be a GOOD year. */}
        <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 74 }}>
          {r.years.map(y => {
            const h = Math.max(4, Math.min(70, y.index * 38));
            const good = y.index >= 1;
            // An adjuster's appraisal, not a machine's harvest: same index, drawn hollow.
            const appr = y.appr === true || (y.parts || []).some(p => p.appr === true);
            return (
              <div key={y.yr} style={{ textAlign: "center", flex: "0 0 46px" }}>
                <div title={(y.yld == null
                      ? `${y.yr} ${y.crop || ""} · ${y.ac} ac · ${(y.index * 100).toFixed(0)}% of the farm that year`
                      : `${y.yr} ${cropLabel} · ${y.ac} ac · ${y.yld} ${unit} vs farm ${y.opMean} ${unit} = ${(y.index * 100).toFixed(0)}%`)
                      + (appr ? " · adjuster appraisal" : "")}
                     style={{ height: h, background: appr ? "#fff" : good ? "#7fb069" : "#c98a8a",
                              border: appr ? `2px dashed ${good ? "#5f8f4c" : "#a86a6a"}` : 0, boxSizing: "border-box",
                              borderRadius: "3px 3px 0 0" }} />
                <div style={{ fontSize: 10.5, color: T.faint, marginTop: 3 }}>
                  {String(y.yr).slice(2)}
                </div>
                {/* Across crops there is no bushel figure to print — 240 and 66 are
                    corn and beans — so the year shows what it grew instead. */}
                <div style={{ fontSize: 10.5, color: T.body, fontWeight: 700 }}>
                  {y.yld == null
                    ? <span style={{ color: T.faint, fontWeight: 600 }}>{yaCropShort(y.crop)}</span>
                    : yaFmtYld(y.yld, crop)}
                </div>
                <div style={{ fontSize: 10, color: T.faint }}>{(y.index * 100).toFixed(0)}%</div>
                {appr && <div style={{ fontSize: 9.5, fontWeight: 800, color: "#2f5f8a" }}>appraised</div>}
              </div>
            );
          })}
        </div>
        {r.years.some(y => y.appr === true || (y.parts || []).some(p => p.appr === true)) && (
          <div style={{ fontSize: 11, color: T.faint, marginTop: 6, lineHeight: 1.5 }}>
            Hollow bars are adjuster appraisals in bushels, used only in a year no machine harvested this field.
          </div>
        )}
      </div>

      {/* EVERY CROP THIS FIELD GROWS, side by side. This is the
          answer to "what's the CV for corn, for soy, for wheat"
          on one piece of ground — three separate readings that
          are never averaged into one. */}
      <div style={{ flex: "1 1 300px", fontSize: 12, color: T.body }}>
        <div style={{ ...uiLabel, marginBottom: 6 }}>THIS FIELD, BY CROP</div>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {["Crop", "Yrs", "vs farm", "Stability", "Typical"].map((h, i) => (
                <th key={h} style={{ textAlign: i === 0 ? "left" : "right", fontSize: 10.5,
                                     letterSpacing: "0.05em", color: T.faint, fontWeight: 700,
                                     padding: "0 8px 4px 0", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(r.crops)
              .filter(([ck]) => ck !== YA_ALL)
              .sort((a, b) => b[1].index - a[1].index)
              .map(([ck, cc]) => {
                const cst = yaSteadyWord(cc.icv);
                const on = ck === crop;
                return (
                  <tr key={ck} style={{ background: on ? "#e8f4e6" : undefined }}>
                    <td style={{ padding: "3px 8px 3px 4px", fontWeight: on ? 800 : 600,
                                 color: T.ink, whiteSpace: "nowrap" }}>{yaCropLabel(ck)}</td>
                    <td style={{ padding: "3px 8px 3px 0", textAlign: "right", color: T.faint,
                                 fontVariantNumeric: "tabular-nums" }}>{cc.n}</td>
                    <td style={{ padding: "3px 8px 3px 0", textAlign: "right", fontWeight: 800,
                                 fontVariantNumeric: "tabular-nums",
                                 color: cc.index >= 1 ? "#2f7d32" : "#8c3a3a" }}>
                      {(cc.index * 100).toFixed(0)}%
                    </td>
                    <td style={{ padding: "3px 8px 3px 0", textAlign: "right", fontWeight: 700,
                                 color: cst.color, whiteSpace: "nowrap" }}>
                      {cst.word}
                      <span style={{ color: T.faint, fontWeight: 500 }}>
                        {cc.icv == null ? "" : ` ${cc.icv.toFixed(2)}`}
                      </span>
                    </td>
                    <td style={{ padding: "3px 0", textAlign: "right",
                                 fontVariantNumeric: "tabular-nums" }}>
                      {yaFmtYld(cc.mean, ck)} <span style={{ color: T.faint, fontSize: 10 }}>{yaUnit(ck)}</span>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        {Object.keys(r.crops).filter(k => k !== YA_ALL).length >= 2 && (() => {
          const cs = Object.entries(r.crops).filter(([k]) => k !== YA_ALL)
            .sort((a, b) => b[1].index - a[1].index);
          const [bk, bv] = cs[0], [wk, wv] = cs[cs.length - 1];
          return (
            <div style={{ marginTop: 6, fontSize: 11.5, color: T.faint, lineHeight: 1.5 }}>
              Indexes {Math.round((bv.index - wv.index) * 100)} pts better in {yaCropLabel(bk).toLowerCase()} than
              in {yaCropLabel(wk).toLowerCase()} — the same acres, two different assets.
            </div>
          );
        })()}
      </div>

      <div style={{ flex: "1 1 230px", fontSize: 12, color: T.body, lineHeight: 1.6 }}>
        <div style={{ ...uiLabel, marginBottom: 6 }}>WHAT IT MEANS</div>
        {q && <div style={{ color: q.color, fontWeight: 700 }}>{q.note}</div>}
        <div style={{ marginTop: 4 }}>
          {isAll ? <>Across everything it grows this field runs </>
                 : <>In {cropLabel.toLowerCase()} this field runs </>}
          <b>{(r.index * 100).toFixed(0)}%</b> of the
          rest of the farm's {isAll ? "" : `${cropLabel.toLowerCase()} `}ground — <b>#{r.rank} of {r.of}</b>
          {r.pctile != null && r.pctile >= 0.75 ? ", top quarter" : r.pctile != null && r.pctile < 0.25 ? ", bottom quarter" : ""}
          {" "}({r.n} year{r.n === 1 ? "" : "s"}, {r.firstYr}–{r.lastYr}).
        </div>
        {r.raw != null && Math.abs(r.raw - r.index) >= 0.02 && (
          <div style={{ marginTop: 4, fontSize: 11.5, color: T.faint, lineHeight: 1.5 }}>
            It actually averaged <b>{(r.raw * 100).toFixed(0)}%</b>, but {r.n} year
            {r.n === 1 ? "" : "s"} only supports {((r.reliability || 0) * 100).toFixed(0)}% of that —
            a season swings a field more than the ground does, so the rest defaults to the farm
            average until more harvests land.
          </div>
        )}
        <div style={{ marginTop: 4 }}>
          {r.icv == null
            ? <>Stability needs {YA_MIN_CV_YEARS} {isAll ? "" : `${cropLabel.toLowerCase()} `}years and this
               has {r.n} — the ranking stands, the dependability read doesn't yet.</>
            : <><b style={{ color: st.color }}>{st.word}</b> — after the season is divided out it moves
               <b> ±{(r.icv * 100).toFixed(0)}%</b> year to year
               {isAll && " within each crop"}.</>}
        </div>
        {r.mean != null && (
          <div style={{ marginTop: 4, color: T.faint }}>
            {Math.round(r.mean)} {unit} typical, {r.p10 == null ? "—" : Math.round(r.p10)} in a bad year
            {r.depend != null && ` (${(r.depend * 100).toFixed(0)}% of typical)`}.
            {r.rawCv != null && ` Raw range ${Math.round(r.lo)}–${Math.round(r.hi)} ${unit}.`}
          </div>
        )}
        {isAll && r.cropSpread != null && (
          <div style={{ marginTop: 4, color: T.faint }}>
            Its standing moves <b>{Math.round(r.cropSpread * 100)} points</b> between crops —
            {r.cropSpread <= 0.10 ? " about the same ground whatever is on it."
              : " it has a crop it clearly prefers."}
          </div>
        )}
        {r.crossN > r.n && (
          <div style={{ marginTop: 5, fontSize: 11, color: T.faint, lineHeight: 1.5 }}>
            Across all {r.crossN} seasons and every crop it has grown this ground indexes{" "}
            {(r.crossPerf * 100).toFixed(0)}% — context only, never the ranking.
          </div>
        )}
      </div>
    </div>
  );
}

// A sheet on a phone, a card on a desk. Prev / Next walk the ranking, so a grower can
// read down their ground one field at a time without going back to the list.
function YaFieldCard({ r, list, crop, cropLabel, isAll, unit, farmName, onClose, onPick, isMobile }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  if (!r) return null;
  const byRank = list.slice().sort((a, b) => a.rank - b.rank);
  const i = byRank.findIndex(x => x.key === r.key);
  const prev = i > 0 ? byRank[i - 1] : null, next = i >= 0 && i < byRank.length - 1 ? byRank[i + 1] : null;
  const q = r.quad ? YA_QUAD[r.quad] : null;
  const pill = { height: 36, padding: "0 14px", borderRadius: 18, border: `1px solid ${T.lineStrong}`, background: "#fff",
                 font: "inherit", fontSize: 13, fontWeight: 700, color: T.ink, cursor: "pointer" };
  return createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(16,26,16,0.45)",
                                    display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label={`${r.field} details`}
        style={{ background: "#fff", width: isMobile ? "100%" : "min(920px, calc(100vw - 48px))",
                 maxHeight: isMobile ? "88vh" : "86vh", overflowY: "auto", fontFamily: T.sans, color: T.ink,
                 borderRadius: isMobile ? "18px 18px 0 0" : 18, boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                 paddingBottom: isMobile ? "env(safe-area-inset-bottom, 0px)" : 0 }}>
        <div style={{ position: "sticky", top: 0, background: "#fff", borderBottom: "1px solid #eef2ee", padding: "12px 16px 10px", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 700, lineHeight: 1.15 }}>{r.field}</div>
              <div style={{ fontSize: 12.5, color: "#4f6a4c", marginTop: 2 }}>
                {farmName ? `${farmName} · ` : ""}{cropLabel} · {Math.round(r.acres)} ac · #{r.rank} of {r.of}
              </div>
            </div>
            <button onClick={onClose} style={pill} aria-label="Close">Close</button>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#fff", background: yaIxColor(r.index) === "#ece6c2" ? "#8a8466" : yaIxColor(r.index),
                           borderRadius: 999, padding: "3px 10px" }}>{yaPct(r.index)} of your average</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: q ? q.color : YA_UNRATED.color, background: q ? q.bg : YA_UNRATED.bg,
                           borderRadius: 999, padding: "3px 10px" }}>{q ? q.label : YA_UNRATED.label}</span>
            <span style={{ flex: 1 }} />
            <button disabled={!prev} onClick={() => prev && onPick(prev.key)} style={{ ...pill, opacity: prev ? 1 : 0.4 }}>‹ #{prev ? prev.rank : ""}</button>
            <button disabled={!next} onClick={() => next && onPick(next.key)} style={{ ...pill, opacity: next ? 1 : 0.4 }}>#{next ? next.rank : ""} ›</button>
          </div>
        </div>
        <div style={{ padding: "14px 16px 18px" }}>
          <YaFieldDetail r={r} crop={crop} cropLabel={cropLabel} isAll={isAll} unit={unit} />
        </div>
      </div>
    </div>,
    document.body
  );
}

// The ranking as a phone reads it: one tap-able line per field. The wide table stays
// on a desk, where its columns fit.
function YaFieldList({ list, onPick, openKey, farmId, nameById }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${T.lineStrong}`, borderRadius: 16, overflow: "hidden" }}>
      {list.map((r, i) => {
        const q = r.quad ? YA_QUAD[r.quad] : YA_UNRATED;
        const st = yaSteadyWord(r.icv);
        return (
          <button key={r.key} onClick={() => onPick(r.key)}
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", font: "inherit", cursor: "pointer",
                     padding: "11px 14px", border: 0, borderTop: i ? "1px solid #eef2ee" : 0,
                     background: r.key === openKey ? "#f2f8f0" : "#fff", minHeight: 52 }}>
            <span style={{ width: 34, fontSize: 12.5, fontWeight: 800, color: T.faint, fontVariantNumeric: "tabular-nums" }}>#{r.rank}</span>
            <span style={{ width: 6, alignSelf: "stretch", borderRadius: 3, background: q.color }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.field}</span>
              <span style={{ display: "block", fontSize: 11.5, color: T.faint }}>
                {!farmId && nameById.get(r.farmer_id) ? `${nameById.get(r.farmer_id)} · ` : ""}{Math.round(r.acres)} ac · {r.n} yr{r.n === 1 ? "" : "s"} · <span style={{ color: st.color, fontWeight: 700 }}>{r.icv == null ? "not rated yet" : st.word.toLowerCase()}</span>
              </span>
            </span>
            <span style={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: r.index >= 1 ? "#2f7d32" : "#a8453c" }}>{yaPct(r.index)}</span>
            <span aria-hidden="true" style={{ color: T.faint, fontSize: 18 }}>›</span>
          </button>
        );
      })}
    </div>
  );
}

function YieldAnalyticsView({ farmers = [], isMobile = false, mode = null, restrictFarmerIds = null }) {
  const isPortal = mode === "portal";
  const [rows, setRows]     = useState(null);   // per field, with a per-crop block
  const [farms, setFarms]   = useState([]);
  const [dropped, setDropped] = useState(null); // partial passes / impossible yields
  const [err, setErr]       = useState(null);
  const [farmId, setFarmId] = useState("");     // "" = all connected farms
  const [quad, setQuad]     = useState("");     // "" = all quadrants
  // THE crop. Not a display toggle — every number on the page is computed from
  // this crop's years alone, so switching it recomputes the ranking, the CVs and
  // the quadrant acres. "" until the data lands, then the commonest crop.
  const [crop, setCrop]     = useState("");
  const [sort, setSort]     = useState({ k: "index", d: -1 });
  const [open, setOpen]     = useState(null);   // expanded field key
  const [qcOpen, setQcOpen] = useState(false);  // excluded-records panel
  const [lens, setLens]     = useState("ground"); // ground | steady | plantdate | variety
  const [seasons, setSeasons] = useState([]);   // operation mean per farm, crop, year
  const [aph, setAph] = useState([]);           // policy APH per farm, crop, crop year

  const nameById = useMemo(() => {
    const m = new Map();
    (farmers || []).forEach(f => { if (f.id && f.name && !m.has(f.id)) m.set(f.id, f.name); });
    return m;
  }, [farmers]);

  // Portal: name the caller's own farms in the request rather than leaving the
  // request unqualified and trusting the server to narrow it. Identical result
  // for a real farmer — but "Preview as" renders this component with an ADMIN
  // token, and an unqualified call from an admin correctly returns the book.
  const scopeQ = useMemo(() => {
    const ids = (restrictFarmerIds || []).filter(Boolean);
    return ids.length ? `&farmer=${ids.map(encodeURIComponent).join(",")}` : "";
  }, [restrictFarmerIds]);

  useEffect(() => {
    if (isPortal && !scopeQ) { setRows([]); setFarms([]); return; }
    let alive = true;
    // Server-side: every jd_* table is RLS-on with no policies (service-role
    // only), so a direct sbFetch here returns [] with no error — which is
    // exactly how this page first shipped reading 'no harvest history' on a
    // farm with 2,807 field-crop-years behind it.
    authedFetch(`/api/market?fn=jd-yieldanalytics${scopeQ}`)
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        if (!d || d.error) { setErr((d && d.error) || 'no response'); return; }
        setRows(d.rows || []);
        setFarms(d.farms || []);
        setDropped(d.dropped || null);
        setSeasons(d.seasons || []);
        setAph(d.aph || []);
      })
      .catch(e => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [scopeQ, isPortal]);

  // Every crop the farms in view actually grow, by how many field-years back it —
  // the tabs offer what the data contains, not a hardcoded list. Scoped to the
  // selected farm so a dairy that only runs corn doesn't get a dead WHEAT tab.
  const cropOptions = useMemo(() => {
    const c = {};
    (rows || []).forEach(r => {
      if (farmId && r.farmer_id !== farmId) return;
      Object.entries(r.crops || {}).forEach(([k, v]) => {
        if (k === YA_ALL) return;                 // the overall view is not a crop
        c[k] = (c[k] || 0) + (v.n || 0);
      });
    });
    // A farm with John Deere seasons but no field old enough to rank (Red Dawn: its
    // history starts in 2024) still has seasons worth showing, so the tabs come from
    // those seasons instead of leaving the page with nothing to pick.
    if (!Object.keys(c).length) {
      (seasons || []).forEach(sn => {
        if (farmId && sn.farmer_id !== farmId) return;
        if (sn.crop) c[sn.crop] = (c[sn.crop] || 0) + (sn.ac || 0);
      });
    }
    const real = Object.entries(c).sort((a, b) => b[1] - a[1]).map(([k]) => k);
    return real.length ? [YA_ALL, ...real] : [];  // overall leads — it's the hero view
  }, [rows, farmId, seasons]);

  // Land on the overall view, and never sit on a crop the selected farm doesn't
  // grow — switching farms with WHEAT held would otherwise empty the table.
  useEffect(() => {
    if (!cropOptions.length) return;
    if (!crop || !cropOptions.includes(crop)) setCrop(cropOptions[0]);
  }, [cropOptions, crop]);

  // The selector lists only farms that actually have scored fields — offering a
  // connected farm with nothing behind it just produces an empty table.
  const farmsWithData = useMemo(
    () => (farms || []).map(f => [f.id, f.name, f.fields, f.acres]),
    [farms]);

  // One row per field THAT GREW THIS CROP, flattened onto the crop's own numbers.
  // A field with no history in the selected crop is absent rather than blank —
  // there is nothing honest to say about its corn if it has never grown corn.
  const scored = useMemo(() => {
    if (!rows || !crop) return [];
    const out = [];
    for (const r of rows) {
      if (farmId && r.farmer_id !== farmId) continue;
      const c = (r.crops || {})[crop];
      if (!c) continue;
      out.push({
        // jd_field_id is what the ground map matches outlines on; without it every
        // outline drew dashed while the list beside it ranked the same fields.
        key: r.key, farmer_id: r.farmer_id, jd_field_id: r.jd_field_id, field: r.field,
        crops: r.crops, domCrop: r.domCrop,
        crossPerf: r.perf, crossCv: r.cv, crossN: r.n,
        crop, ...c,
      });
    }
    return out;
  }, [rows, crop, farmId]);

  // Rank is served per FARM per crop. Viewing all farms at once, a "#4 of 62"
  // from one farm next to a "#4 of 9" from another is not a ranking anyone can
  // read down the column — so the standing is re-struck across whatever is in
  // view, and the footnote says which it is.
  const rankedAll = useMemo(() => {
    // Rank is struck over the FARM-SCOPED set and BEFORE the quadrant filter, so
    // clicking "Below · Unstable" doesn't renumber the survivors 1..n and make a
    // field look like the best corn on the farm when it's the worst. With one
    // farm selected this reproduces the server's per-farm rank exactly. The map,
    // the chart and the field card all read this whole list; only the table is
    // narrowed by a box.
    const ranked = scored.slice().sort((a, b) => b.index - a.index);
    const N = ranked.length;
    const rank = new Map();
    ranked.forEach((r, i) => rank.set(r.key, { rank: i + 1, of: N,
      pctile: N > 1 ? (N - (i + 1)) / (N - 1) : null }));
    return scored.map(r => ({ ...r, ...rank.get(r.key) }));
  }, [scored]);

  const shown = useMemo(() => {
    let out = rankedAll;
    if (quad) out = out.filter(r => (quad === "unrated" ? !r.quad : r.quad === quad));

    const k = sort.k, d = sort.d;
    const val = (r) => k === "index" ? r.index : k === "icv" ? (r.icv ?? 99)
              : k === "rawcv" ? (r.rawCv ?? 99) : k === "spread" ? (r.cropSpread ?? -1)
              : k === "typical" ? (r.mean ?? -1) : k === "depend" ? (r.depend ?? -1)
              : k === "n" ? r.n : k === "acres" ? r.acres : r.index;
    // Text columns sort alphabetically; Type sorts by how good the quadrant is,
    // which is more useful than sorting its label.
    const QORD = { anchor: 3, boom: 2, structural: 1, fragile: 0 };
    if (k === "field") return out.slice().sort((a, b) => String(a.field).localeCompare(String(b.field)) * d);
    if (k === "quad")  return out.slice().sort((a, b) => ((QORD[a.quad] ?? -1) - (QORD[b.quad] ?? -1)) * d
                                                        || String(a.field).localeCompare(String(b.field)));
    return out.slice().sort((a, b) => (val(a) - val(b)) * d);
  }, [rankedAll, quad, sort]);

  // Acres, not field counts — acres are what you farm. Fields short of a CV carry
  // no quadrant, so they're counted separately rather than folded into a bucket
  // they haven't earned.
  const totals = useMemo(() => {
    // Over every scored field of the crop, NOT the box-filtered list — picking a box
    // used to zero the other four, which read as the acres vanishing.
    const t = { anchor: 0, boom: 0, structural: 0, fragile: 0, acres: 0, unrated: 0, unratedN: 0 };
    for (const r of rankedAll) {
      t.acres += r.acres;
      if (r.quad) t[r.quad] += r.acres;
      else { t.unrated += r.acres; t.unratedN++; }
    }
    return t;
  }, [rankedAll]);
  const shownAcres = useMemo(() => shown.reduce((s, r) => s + r.acres, 0), [shown]);

  // How many fields each crop tab would actually show, so a tab never promises a
  // table it can't fill. Counted on fields, not field-years, because that's what
  // the tab opens onto.
  const scoredCountByCrop = useMemo(() => {
    const c = {};
    (rows || []).forEach(r => {
      if (farmId && r.farmer_id !== farmId) return;
      Object.keys(r.crops || {}).forEach(k => { c[k] = (c[k] || 0) + 1; });
    });
    return c;
  }, [rows, farmId]);

  const isAll     = crop === YA_ALL;
  // Top and bottom of the ranking, taken from the rank itself rather than the
  // current sort, so the callout says the same thing whatever column you clicked.
  const heroes = useMemo(() => {
    const byRank = shown.slice().sort((a, b) => a.rank - b.rank);
    const k = Math.min(5, Math.floor(byRank.length / 2));
    return { best: byRank.slice(0, k), worst: byRank.slice(-k).reverse() };
  }, [shown]);

  const cropLabel = isAll ? "All crops" : (crop ? yaCropLabel(crop) : "crop");
  // How the crop reads inside a sentence: "every corn harvest", but "every
  // harvest" when the view spans all of them.
  const cropPhrase = isAll ? "" : `${cropLabel.toLowerCase()} `;
  const unit = yaUnit(crop);
  const droppedTotal = dropped ? (dropped.total || 0) : 0;

  // ── ONE-PAGE PRINTABLE REPORT ─────────────────────────────────────────────
  // Same window.open + window.print() pattern as the APH history and the LRP
  // monthly summary — no new dependency in a repo that keeps 4 runtime deps.
  //
  // Deliberately ONE page: this is the conversation-starter you hand a grower,
  // not the whole table. Under 24 fields it prints every one of them; above
  // that it prints the two ends, because a 75-row list at 6pt is not a report.
  const printReport = useCallback(() => {
    const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
    const LOGO_URL = origin + "/Clover_Ag_Logo1_transparent.png";
    const printDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

    const farmName = farmId ? (nameById.get(farmId) || "—") : "All connected farms";
    const byRank = rankedAll.slice().sort((a, b) => a.rank - b.rank);
    const compact = byRank.length <= 24;

    const rowHtml = (r) => {
      const st = yaSteadyWord(r.icv);
      const cls = r.index >= 1 ? "up" : "down";
      return `<tr>
        <td class="rk">${r.rank}</td>
        <td class="fld">${esc(r.field)}${!farmId ? `<span class="farm">${esc(nameById.get(r.farmer_id) || "")}</span>` : ""}</td>
        <td class="num ${cls}">${(r.index * 100).toFixed(0)}%</td>
        <td class="st" style="color:${st.color}">${esc(st.word)}</td>
        <td class="num ac">${r.acres.toFixed(0)}</td>
      </tr>`;
    };
    const tbl = (title, color, list) => `
      <div class="col">
        <div class="col-head" style="border-color:${color};color:${color}">${esc(title)}</div>
        <table class="rank-table">
          <thead><tr><th>#</th><th>Field</th><th class="num">vs&nbsp;farm</th><th>Stability</th><th class="num">Acres</th></tr></thead>
          <tbody>${list.map(rowHtml).join("")}</tbody>
        </table>
      </div>`;

    const body = compact
      ? tbl(`Every ${isAll ? "" : cropLabel.toLowerCase() + " "}field, best to weakest`, "#2d6a2d", byRank)
      : tbl("Best ground", "#2f7d32", byRank.slice(0, 15))
        + tbl("Weakest ground", "#8c3a3a", byRank.slice(-15).reverse());

    const quads = Object.entries(YA_QUAD).map(([k, q]) => {
      const ac = totals[k], pct = totals.acres > 0 ? ac / totals.acres * 100 : 0;
      return `<div class="q" style="border-left-color:${q.color}">
        <div class="q-n" style="color:${q.color}">${Math.round(ac).toLocaleString()}<span>ac</span></div>
        <div class="q-l">${esc(q.label)}</div>
        <div class="q-p">${pct.toFixed(0)}% of acres</div>
      </div>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Field Productivity — ${esc(farmName)}</title>
<style>
  @page { size: letter portrait; margin: 0.45in; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; color:#1a2e1a; margin:0; padding:0; background:#fff; }
  .header { display:flex; align-items:center; gap:14px; border-bottom:2px solid #2d6a2d; padding-bottom:10px; margin-bottom:12px; }
  .logo { height:42px; }
  .title { font-size:19px; font-weight:800; letter-spacing:-0.01em; }
  .subtitle { font-size:12.5px; font-weight:700; color:#2d6a2d; margin-top:1px; }
  .asof { font-size:9.5px; color:#60735f; margin-top:2px; }
  .title-block { flex:1; }
  .print-btn { background:#2d6a2d; color:#fff; border:0; border-radius:7px; padding:8px 14px; font-size:12px; font-weight:700; cursor:pointer; }
  .lede { font-size:10.5px; color:#3f5540; line-height:1.5; margin-bottom:11px; max-width:none; }
  .quads { display:flex; gap:7px; margin-bottom:13px; }
  .q { flex:1; border:1px solid #dbe5db; border-left:3px solid; border-radius:5px; padding:6px 8px; }
  .q-n { font-size:16px; font-weight:800; line-height:1.1; }
  .q-n span { font-size:8.5px; font-weight:700; margin-left:2px; }
  .q-l { font-size:9.5px; font-weight:800; margin-top:1px; }
  .q-p { font-size:8.5px; color:#60735f; }
  .cols { display:flex; gap:16px; align-items:flex-start; }
  .col { flex:1; min-width:0; }
  .col-head { font-size:10px; font-weight:800; letter-spacing:0.06em; text-transform:uppercase;
              border-bottom:2px solid; padding-bottom:4px; margin-bottom:3px; }
  .rank-table { width:100%; border-collapse:collapse; }
  .rank-table th { font-size:8px; letter-spacing:0.04em; color:#60735f; text-align:left; font-weight:700;
                   padding:2px 4px 3px 0; text-transform:uppercase; }
  .rank-table td { font-size:10px; padding:2.5px 4px 2.5px 0; border-bottom:1px solid #eef2ee; }
  .rank-table .num { text-align:right; font-variant-numeric:tabular-nums; }
  .rank-table td.rk { color:#60735f; font-weight:700; width:16px; }
  .rank-table td.fld { font-weight:700; max-width:120px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .rank-table td.fld .farm { display:block; font-weight:400; font-size:8px; color:#60735f; }
  .rank-table td.num.up { color:#2f7d32; font-weight:800; }
  .rank-table td.num.down { color:#8c3a3a; font-weight:800; }
  .rank-table td.st { font-size:9px; font-weight:700; }
  .rank-table td.ac { color:#5a6a5a; }
  .method { margin-top:13px; padding-top:8px; border-top:1px solid #d6e0d6; font-size:8.5px; color:#60735f; line-height:1.5; }
  .footer { margin-top:8px; display:flex; justify-content:space-between; font-size:9px; color:#60735f; }
  .footer .brand { font-weight:700; color:#2d6a2d; }
  @media print { .no-print { display:none !important; } }
</style></head>
<body>
  <div class="header">
    <img class="logo" src="${LOGO_URL}" alt="Clover Ag Risk Management" onerror="this.style.display='none'"/>
    <div class="title-block">
      <div class="title">Field Productivity Report</div>
      <div class="subtitle">${esc(farmName)} &middot; ${esc(isAll ? "All crops" : cropLabel)}</div>
      <div class="asof">${rankedAll.length} fields &middot; ${Math.round(totals.acres).toLocaleString()} acres &middot;
        Source: John Deere Operations Center harvest records &middot; As of ${printDate}</div>
    </div>
    <button class="print-btn no-print" onclick="window.print()">Print / Save as PDF</button>
  </div>

  <div class="lede">
    Every harvest is scored against what the rest of this operation did with the <b>same crop the same year</b>,
    so season and crop both cancel out and one number ranks every field. <b>100% is this farm's own average</b> —
    115% means the ground yields 15% better than the rest of the farm, whatever the weather did.
    <b>Stability</b> is how much that standing moves year to year${isAll ? ", pooled within each crop so a rotation isn't mistaken for inconsistency" : ""}.
    Fields with few harvest years are pulled toward the farm average, so no field reaches either end of this list on one lucky season.
  </div>

  <div class="quads">${quads}</div>
  <div class="cols">${body}</div>

  <div class="method">
    <b>Method.</b> Field index = field yield &divide; this operation's acre-weighted average for that crop that year,
    averaged over the field's harvest years and adjusted for how many years stand behind it.
    ${dropped && droppedTotal > 0 ? `${droppedTotal.toLocaleString()} machine records were excluded as partial passes,
    records spanning more than one field, or yields outside a physical range for the crop.` : ""}
    Yield-monitor data is not calibrated scale weight; use this to compare ground within this farm, not as absolute bushels.
  </div>
  <div class="footer">
    <div class="brand">Clover Ag Risk Management &middot; SILAS</div>
    <div>(585) 233-1066 &middot; office@cloverag.com</div>
  </div>
  <script>window.addEventListener('load',function(){setTimeout(function(){try{window.print();}catch(e){}},500);});</script>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) { alert("Popup blocked — allow popups for this site, then click Print report again."); return; }
    writePrintWindow(w, html);
  }, [rankedAll, totals, farmId, nameById, isAll, cropLabel, dropped, droppedTotal]);

  // The map draws one farm's outlines. A portal grower with one operation, or an agent
  // who has picked a farm, gets it at once; across several farms the map asks which.
  const mapFarmId = farmId || (farmsWithData.length === 1 ? farmsWithData[0][0] : "");
  const openRow = open ? rankedAll.find(r => r.key === open) || null : null;
  const nFarmsInView = farmId ? 1 : new Set((seasons || []).map(s => s.farmer_id)).size;
  const clickSort = (k) => setSort(s => ({ k, d: s.k === k ? -s.d : -1 }));
  const th = (label, k, right) => (
    <th onClick={() => clickSort(k)}
        style={{ cursor: "pointer", textAlign: right ? "right" : "left", padding: "8px 10px", fontSize: 10.5,
                 letterSpacing: "0.05em", color: T.faint, borderBottom: `2px solid ${T.line}`,
                 whiteSpace: "nowrap", userSelect: "none" }}>
      {label}{sort.k === k ? (sort.d > 0 ? " ▲" : " ▼") : ""}
    </th>
  );
  const td = { padding: "8px 10px", fontSize: 12.5, borderBottom: `1px solid ${T.lineSoft}`, color: T.ink };

  // ── LENS SWITCH ───────────────────────────────────────────────────────────
  // Three questions about the same harvests: which GROUND yields, which VARIETY
  // yielded, and what PLANTING DATE yielded. One nav item, because a grower does
  // not know to go looking for a second screen.
  //
  // This sits ABOVE the guards below on purpose. Those guards — 3+ seasons, 3+
  // peer fields — are what the FIELD index needs to mean anything; grouping one
  // season's harvests by the variety planted needs none of that, and a farm with
  // two seasons has a perfectly good variety chart it would otherwise never see.
  const lensTabs = (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
      {[["ground", "Ground", "Your fields on the map, best to weakest"],
        ["steady", "Steadiness", "Good ground vs. dependable ground"],
        ["plantdate", "Planting", "What planting timing returned"],
        ["variety", "Variety", "Which hybrid performed"]].map(([k, label, sub]) => {
        const on = k === lens;
        return (
          <button key={k} onClick={() => setLens(k)} title={sub}
            style={{ cursor: "pointer", font: "inherit", padding: "7px 14px", borderRadius: 999,
                     border: `1px solid ${on ? "#2d4a2d" : T.line}`,
                     background: on ? "#2d4a2d" : T.surface, color: on ? "#fff" : T.body,
                     fontSize: 12.5, fontWeight: on ? 800 : 600 }}>
            {label}
          </button>
        );
      })}
    </div>
  );

  if (lens === "variety" || lens === "plantdate") return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ ...uiLabel, marginBottom: 2 }}>{isPortal ? "FROM YOUR JOHN DEERE DATA" : "JOHN DEERE CONNECTED FARMS"}</div>
        <h2 style={{ margin: 0, color: T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>Yield Analytics</h2>
      </div>
      {lensTabs}
      <YieldFactorsPanel lens={lens} isMobile={isMobile} mode={mode}
        restrictFarmerIds={restrictFarmerIds} nameById={nameById} />
    </div>
  );

  if (err)   return <div style={{ ...uiCard(), color: T.danger, fontSize: 13 }}>Couldn't load yield history: {err}</div>;
  if (!rows) return <div style={{ ...uiCard(), color: T.faint, fontSize: 13 }}>Reading harvest history…</div>;

  // Seasons on file but no field with enough years to rank yet. The season itself is
  // real and recent (Red Dawn: 4,342 ac of 2026 silage), so it shows, and only the
  // field ranking waits. Before this, such a farm got the bare "not enough harvest
  // history" panel and nothing else.
  if (!rows.length && (seasons || []).length > 0 && cropOptions.length > 0) {
    const yrs = [...new Set((seasons || []).filter(sn => !farmId || sn.farmer_id === farmId).map(sn => sn.yr))].sort();
    return (
      <div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ ...uiLabel, marginBottom: 2 }}>{isPortal ? "FROM YOUR JOHN DEERE DATA" : "JOHN DEERE CONNECTED FARMS"}</div>
          <h2 style={{ margin: 0, color: T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>Yield Analytics</h2>
        </div>
        {lensTabs}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
          {cropOptions.map(c => {
            const on = c === crop;
            return (
              <button key={c} onClick={() => setCrop(c)}
                style={{ cursor: "pointer", font: "inherit", padding: "7px 14px", borderRadius: 999,
                         border: `1px solid ${on ? "#2d4a2d" : T.line}`, background: on ? "#2d4a2d" : T.surface,
                         color: on ? "#fff" : T.body, fontSize: 12.5, fontWeight: on ? 800 : 600 }}>
                {yaCropLabel(c)}
              </button>
            );
          })}
        </div>
        <YaSeasonHeader seasons={seasons} aph={aph} aphFarmId={farmId || (isPortal && (restrictFarmerIds || []).length === 1 ? restrictFarmerIds[0] : "")}
          crop={crop} farmId={farmId} cropOptions={cropOptions}
          onPickCrop={(c) => setCrop(c)} nFarmsInView={nFarmsInView} isMobile={isMobile} />
        <div style={{ ...uiCard(), fontSize: 13, color: T.body, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 800, color: T.ink, marginBottom: 4 }}>Field rankings start once a field has {YA_MIN_YEARS} harvest years</div>
          Your John Deere harvest data on file covers {yrs.length === 1 ? yrs[0] : `${yrs[0]} to ${yrs[yrs.length - 1]}`}, and no field
          has {YA_MIN_YEARS} separate harvest years in it yet. One or two seasons can't tell good ground from a good year, so the
          ground map and steadiness views fill in on their own as more harvests come in. Planting and Variety above work now.
        </div>
      </div>
    );
  }

  // No rows on the portal side is usually "no John Deere connection", not "not
  // enough seasons" — and those are different problems with different fixes.
  if (!rows.length && isPortal) return (
    <JdConnectPanel isMobile={isMobile} restrictFarmerIds={restrictFarmerIds} what="harvest history"
      connectedTitle="Not enough harvest history yet"
      connectedNote={`A field is scored against what the rest of your operation did with the same crop the same year, so it needs ${YA_MIN_YEARS}+ harvest years, ${YA_MIN_ACRES}+ acres, and at least ${YA_MIN_PEERS} other fields of that crop to compare against. One or two seasons can't tell ground apart from weather. Your fields appear here on their own as John Deere sends more years.`} />
  );

  if (!rows.length) return (
    <div style={{ ...uiCard(), fontSize: 13, color: T.body, lineHeight: 1.6 }}>
      <div style={{ fontWeight: 800, color: T.ink, marginBottom: 6 }}>Not enough harvest history yet</div>
      A field needs {YA_MIN_YEARS}+ harvest years, {YA_MIN_ACRES}+ acres, and at least {YA_MIN_PEERS} other
      fields of the same crop that year before an index means anything. Once more John Deere seasons sync,
      fields will appear here automatically.
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ ...uiLabel, marginBottom: 2 }}>{isPortal ? "FROM YOUR JOHN DEERE DATA" : "JOHN DEERE CONNECTED FARMS"}</div>
        <h2 style={{ margin: 0, color: T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>Yield Analytics</h2>
        <div style={{ fontSize: 12.5, color: T.body, marginTop: 5, maxWidth: 800, lineHeight: 1.55 }}>
          {isAll
            ? <>Which of your fields yield best, whatever was planted. Each harvest is compared with the rest of
               the farm for the same crop the same year, so a good season or a high-yielding crop doesn{"’"}t make
               a field look better than it is. <b>Steadiness</b> is how much a field swings from year to year,
               judged within each crop.</>
            : <>Each {cropPhrase}harvest is compared with the rest of the farm{"’"}s {cropLabel.toLowerCase()} the same
               year, so the weather of the season drops out and what{"’"}s left is the ground. <b>How good</b> is the
               field{"’"}s average against the farm; <b>steadiness</b> is how much it swings. Two fields with the same
               average can be very different to farm.</>}
        </div>
      </div>

      {lensTabs}

      {/* ── The crop IS the analysis, so it leads the page ──────────────────────
          A dropdown reads as a display option. These read as what they are: four
          separate answers about the same acres. */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
        {cropOptions.map(c => {
          const on = c === crop;
          const nF = scoredCountByCrop[c] || 0;
          const all = c === YA_ALL;
          return (
            <button key={c} onClick={() => { setCrop(c); setQuad(""); setOpen(null); }}
              style={{ cursor: "pointer", font: "inherit", padding: "7px 14px", borderRadius: 999,
                       border: `1px solid ${on ? "#2d4a2d" : all ? "#9dbd94" : T.line}`,
                       background: on ? "#2d4a2d" : T.surface, color: on ? "#fff" : T.body,
                       fontSize: 12.5, fontWeight: on || all ? 800 : 600, letterSpacing: "0.01em",
                       marginRight: all ? 8 : 0 }}>
              {yaCropLabel(c)}
              <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 600, opacity: on ? 0.75 : 0.6 }}>
                {nF}
              </span>
            </button>
          );
        })}
      </div>

      <YaSeasonHeader seasons={seasons} aph={aph} aphFarmId={mapFarmId} crop={crop} farmId={farmId} cropOptions={cropOptions}
        onPickCrop={(c) => { setCrop(c); setQuad(""); setOpen(null); }} nFarmsInView={nFarmsInView} isMobile={isMobile} />

      {lens === "ground" && (mapFarmId
        ? <YaGroundMap farmerId={mapFarmId} scored={rankedAll} cropLabel={cropLabel} onPick={setOpen} focusKey={open} isMobile={isMobile} />
        : (
          <div style={{ background: "#fff", border: `1px solid ${T.lineStrong}`, borderRadius: 16, padding: "14px 16px", marginBottom: 12,
                        display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 240px", fontSize: 13, color: T.body }}>
              <b style={{ color: T.ink }}>Pick a farm to see its fields on the map.</b> The ranking below covers every farm in view.
            </div>
            <select value={farmId} onChange={e => { setFarmId(e.target.value); setOpen(null); }}
              style={{ fontSize: 13, padding: "8px 10px", borderRadius: 10, border: `1px solid ${T.lineStrong}`, background: "#fff", color: T.ink, fontFamily: "inherit", maxWidth: "100%" }}>
              <option value="">Choose a farm…</option>
              {farmsWithData.map(([id, nm]) => <option key={id} value={id}>{nm}</option>)}
            </select>
          </div>
        ))}

      {lens === "ground" && rankedAll.length > 0 && (
        <YaFieldTiles scored={rankedAll} cropLabel={cropLabel} onPick={setOpen} openKey={open} isMobile={isMobile} />
      )}

      {lens === "steady" && rankedAll.length > 0 && (
        <YaSteadinessChart scored={rankedAll} onPick={setOpen} openKey={open} cropLabel={cropLabel} isMobile={isMobile} />
      )}

      {/* ── THE HERO SHOT: best ground, worst ground ─────────────────────────
          Ranked on the reliability-adjusted index, so a field cannot reach
          either end of this list on one lucky or one unlucky season. */}
      {lens === "ground" && shown.length >= 6 && !quad && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          {[["Best ground", "#2f7d32", "#e8f4e6", heroes.best],
            ["Weakest ground", "#8c3a3a", "#f8e9e9", heroes.worst]].map(([title, color, bg, list]) => (
            <div key={title} style={{ flex: "1 1 340px", minWidth: 300, background: T.surface,
                       border: `1px solid ${T.line}`, borderLeft: `4px solid ${color}`,
                       borderRadius: T.r.md, padding: "10px 12px" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color, letterSpacing: "0.05em",
                            textTransform: "uppercase", marginBottom: 7 }}>
                {title} · {isAll ? "all crops" : cropLabel.toLowerCase()}
              </div>
              {list.map(r => (
                <div key={r.key} onClick={() => setOpen(r.key)}
                     style={{ display: "flex", alignItems: "baseline", gap: 8, cursor: "pointer",
                              padding: "3px 0", fontSize: 12.5 }}>
                  <span style={{ fontWeight: 800, color, fontVariantNumeric: "tabular-nums",
                                 minWidth: 30 }}>#{r.rank}</span>
                  <span style={{ fontWeight: 700, color: T.ink, flex: 1, overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.field}</span>
                  <span style={{ fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>
                    {(r.index * 100).toFixed(0)}%
                  </span>
                  <span style={{ fontSize: 10.5, color: T.faint, minWidth: 52, textAlign: "right" }}>
                    {r.acres.toFixed(0)} ac
                  </span>
                </div>
              ))}
              <div style={{ fontSize: 10.5, color: T.faint, marginTop: 6, lineHeight: 1.5 }}>
                {list.length ? <>{Math.round(list.reduce((s, r) => s + r.acres, 0)).toLocaleString()} acres
                  {title === "Best ground"
                    ? " — where to push inputs and what to look for when buying"
                    : " — where a good year still won't save you"}</> : "—"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Quadrant summary: acres, not field counts — acres are what you farm ── */}
      {lens === "steady" && totals.unrated < totals.acres && <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        {Object.entries(YA_BOX).map(([k, q]) => {
          const ac = k === "unrated" ? totals.unrated : totals[k], pct = totals.acres > 0 ? ac / totals.acres * 100 : 0;
          const on = quad === k;
          return (
            <button key={k} onClick={() => setQuad(on ? "" : k)} title={q.note}
              style={{ flex: "1 1 168px", minWidth: 150, textAlign: "left", cursor: "pointer",
                       background: on ? q.bg : T.surface, border: `1px solid ${on ? q.color : T.line}`,
                       borderLeft: `4px solid ${q.color}`, borderRadius: T.r.md, padding: "10px 12px",
                       font: "inherit" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: q.color, fontVariantNumeric: "tabular-nums" }}>
                {Math.round(ac).toLocaleString()}<span style={{ fontSize: 11, fontWeight: 700 }}> ac</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>{q.label}</div>
              <div style={{ fontSize: 10.5, color: T.faint }}>{pct.toFixed(0)}% of {cropLabel.toLowerCase()} acres</div>
            </button>
          );
        })}
      </div>}

      {/* ── Scope: which farm, and which quadrant ── */}
      <div style={{ ...uiCard(), padding: "10px 12px", marginBottom: 12, display: "flex", flexWrap: "wrap",
                    gap: 10, alignItems: "center" }}>
        {farmsWithData.length > 1 && <select value={farmId} onChange={e => { setFarmId(e.target.value); setOpen(null); }}
          style={{ fontSize: 12.5, padding: "5px 8px", borderRadius: 7, border: `1px solid ${T.line}`,
                   background: T.surface, color: T.ink, fontFamily: "inherit" }}>
          <option value="">All connected farms ({farmsWithData.length})</option>
          {farmsWithData.map(([id, nm, nf, ac]) => (
            <option key={id} value={id}>{nm} — {nf} fields · {Number(ac).toLocaleString()} ac</option>
          ))}
        </select>}
        {quad && (
          <button onClick={() => setQuad("")}
            style={{ fontSize: 11.5, padding: "5px 10px", borderRadius: 7, cursor: "pointer",
                     border: `1px solid ${YA_BOX[quad].color}`, background: YA_BOX[quad].bg,
                     color: YA_BOX[quad].color, fontWeight: 700, fontFamily: "inherit" }}>
            {YA_BOX[quad].label} ✕
          </button>
        )}
        <span style={{ fontSize: 11.5, color: T.faint }}>
          {shown.length} {cropLabel.toLowerCase()} field{shown.length === 1 ? "" : "s"} ·{" "}
          {Math.round(shownAcres).toLocaleString()} ac
          {totals.unratedN > 0 && (
            <span title={`These have ${YA_MIN_CROP_YEARS} year(s) of ${cropLabel} — enough to rank, not enough for a stability read. ${YA_MIN_CV_YEARS} years does it.`}
                  style={{ color: "#8a5e10" }}>
              {" "}· {totals.unratedN} not yet rated for stability
            </span>
          )}
        </span>
        {/* Say what ISN'T here. A farm that connected recently can have most of
            its ground short of a third season, and silently omitting it reads as
            broken data rather than as young history. */}
        {(() => {
          const scope = farmId ? farms.filter(f => f.id === farmId) : farms;
          const nF = scope.reduce((s, f) => s + (f.skippedYears || 0), 0);
          const nA = scope.reduce((s, f) => s + (f.skippedYearsAcres || 0), 0);
          if (!nF) return null;
          return (
            <span title={`A field needs ${YA_MIN_YEARS} harvest years before stability means anything — two points only give you the gap between them. These will appear as more seasons sync.`}
                  style={{ fontSize: 11.5, color: "#8a5e10", fontWeight: 600 }}>
              · {nF} more field{nF === 1 ? "" : "s"} ({nA.toLocaleString()} ac) waiting on a {YA_MIN_YEARS}rd harvest year
            </span>
          );
        })()}
        {/* Say what was thrown out, and let it be inspected — a filter you can't
            audit is just a different way of being wrong quietly. */}
        <button onClick={printReport} disabled={!shown.length}
          title={farmId ? "One-page report for this farm — print or save as PDF"
                        : "Pick a farm first for a grower-ready report; this will print the whole book"}
          style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 700, padding: "5px 11px",
                   borderRadius: 7, cursor: shown.length ? "pointer" : "not-allowed",
                   border: "1px solid #bcdcb4", background: "#eef7e8", color: "#2d4a2d",
                   fontFamily: "inherit", opacity: shown.length ? 1 : 0.5 }}>
          🖨 Print report
        </button>
        {dropped && droppedTotal > 0 && (
          <button onClick={() => setQcOpen(o => !o)}
            style={{ fontSize: 11.5, padding: "4px 9px", borderRadius: 7, cursor: "pointer",
                     border: `1px solid ${qcOpen ? "#bcdcb4" : T.line}`, fontFamily: "inherit",
                     background: qcOpen ? "#eef7e8" : T.surface, color: T.body }}>
            {droppedTotal.toLocaleString()} records excluded {qcOpen ? "▲" : "▼"}
          </button>
        )}

      </div>

      {/* ── What the quality filter refused, and why ─────────────────────────
          Every number on this page depends on these exclusions, so they are
          shown rather than asserted. If a real harvest is being thrown out, it
          is visible here instead of silently missing from a ranking. */}
      {qcOpen && dropped && (
        <div style={{ ...uiCard(), padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ ...uiLabel, marginBottom: 6 }}>EXCLUDED HARVEST RECORDS</div>
          <div style={{ fontSize: 12, color: T.body, lineHeight: 1.6, marginBottom: 10, maxWidth: 840 }}>
            A record only counts if it is a clean measurement of <b>one</b> field. The field is its own
            yardstick — no fixed acre threshold serves both a 6-acre corner and a 250-acre block — and the
            yardstick is everything harvested off it in a year, all crops summed, so a field split between
            corn and beans still reads as one field rather than two half-measurements.
            {dropped.acres > 0 && <> {Math.round(dropped.acres).toLocaleString()} recorded acres refused.</>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {Object.entries(dropped.byReason || {}).sort((a, b) => b[1] - a[1]).map(([why, n]) => (
              <span key={why} style={{ fontSize: 11.5, fontWeight: 700, color: "#8a5e10",
                    background: "#fbf1dc", border: "1px solid #e6d3a8", borderRadius: 6, padding: "3px 9px" }}>
                {n.toLocaleString()} {why}
              </span>
            ))}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", minWidth: 560, width: "100%" }}>
              <thead>
                <tr>
                  {["Field", "Crop", "Year", "Record acres", "Field is", "Reported", "Refused because"]
                    .map((h, i) => (
                    <th key={h} style={{ textAlign: i >= 3 && i <= 5 ? "right" : "left", padding: "4px 10px 6px 0",
                                         fontSize: 10, letterSpacing: "0.05em", color: T.faint,
                                         borderBottom: `1px solid ${T.line}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(dropped.examples || []).map((e, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 10px 4px 0", fontSize: 12, fontWeight: 600, color: T.ink }}>{e.field || "—"}</td>
                    <td style={{ padding: "4px 10px 4px 0", fontSize: 12, color: T.body }}>{yaCropLabel(e.crop)}</td>
                    <td style={{ padding: "4px 10px 4px 0", fontSize: 12, color: T.body }}>{e.yr}</td>
                    <td style={{ padding: "4px 10px 4px 0", fontSize: 12, textAlign: "right", fontWeight: 700,
                                 fontVariantNumeric: "tabular-nums", color: "#8c3a3a" }}>{e.acres}</td>
                    <td style={{ padding: "4px 10px 4px 0", fontSize: 12, textAlign: "right",
                                 fontVariantNumeric: "tabular-nums", color: T.faint }}>
                      {e.fieldAcres == null ? "—" : e.fieldAcres}
                    </td>
                    <td style={{ padding: "4px 10px 4px 0", fontSize: 12, textAlign: "right",
                                 fontVariantNumeric: "tabular-nums" }}>
                      {e.yld} <span style={{ color: T.faint, fontSize: 10 }}>{yaUnit(e.crop)}</span>
                    </td>
                    <td style={{ padding: "4px 0", fontSize: 11.5, color: T.body }}>{e.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {droppedTotal > (dropped.examples || []).length && (
            <div style={{ fontSize: 11, color: T.faint, marginTop: 8 }}>
              Showing {(dropped.examples || []).length} of {droppedTotal.toLocaleString()}.
            </div>
          )}
        </div>
      )}

      {isMobile ? (
        shown.length
          ? <YaFieldList list={shown} onPick={setOpen} openKey={open} farmId={farmId} nameById={nameById} />
          : <div style={{ ...uiCard(), color: T.faint, fontSize: 13, textAlign: "center" }}>
              {quad ? <>No {cropLabel.toLowerCase()} fields in this box{farmId ? " on this farm" : ""}.</>
                    : <>No field has {YA_MIN_CROP_YEARS}+ years of {cropLabel.toLowerCase()} yet{farmId ? " on this farm" : ""}.</>}
            </div>
      ) : (
      <div style={{ ...uiCard(), padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
          <thead>
            <tr>
              {th("Field", "field")}
              {th("Rank", "index", 1)}
              {th("Type", "quad")}
              {th("vs farm", "index", 1)}
              {th("Stability", "icv", 1)}
              {/* Bushels never cross crops, so the overall view shows the field's
                  standing in each crop instead of a number that can't exist. */}
              {isAll ? th("By crop", "spread") : th("Swing", "rawcv", 1)}
              {!isAll && th(`Typical ${unit}`, "typical", 1)}
              {!isAll && th("Bad year", "depend", 1)}
              {th("Yrs", "n", 1)}
              {th("Acres", "acres", 1)}
            </tr>
          </thead>
          <tbody>
            {!shown.length && (
              <tr><td colSpan={isAll ? 8 : 10} style={{ ...td, color: T.faint, padding: "18px 12px", textAlign: "center" }}>
                {quad
                  ? <>No {cropLabel.toLowerCase()} fields in this category{farmId ? " on this farm" : ""}.</>
                  : <>No field has {YA_MIN_CROP_YEARS}+ years of {cropLabel.toLowerCase()} yet{farmId ? " on this farm" : ""}.</>}
              </td></tr>
            )}
            {shown.map(r => {
              const q = r.quad ? YA_QUAD[r.quad] : null;
              const st = yaSteadyWord(r.icv);
              const isOpen = open === r.key;
              return (
                <React.Fragment key={r.key}>
                  <tr onClick={() => setOpen(r.key)} style={{ cursor: "pointer",
                      background: isOpen ? T.surfaceAlt : undefined }}>
                    <td style={{ ...td, fontWeight: 700 }}>
                      {r.field}
                      {!farmId && farmsWithData.length > 1 && <div style={{ fontSize: 10.5, color: T.faint, fontWeight: 500 }}>
                        {nameById.get(r.farmer_id) || "—"}</div>}
                    </td>
                    {/* RANK — the standing Kevin asked for: this field's multi-year
                        index against every other field of the SAME crop, so it is a
                        record over all the years, not last season's leaderboard. */}
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                        title={`${r.rank} of ${r.of} ${cropLabel.toLowerCase()} fields${farmId ? " on this farm" : " in view"}, ranked on the multi-year index`}>
                      <span style={{ fontWeight: 800, fontSize: 13.5,
                                     color: r.pctile == null ? T.body
                                          : r.pctile >= 0.75 ? "#2f7d32"
                                          : r.pctile >= 0.25 ? T.body : "#8c3a3a" }}>
                        #{r.rank}
                      </span>
                      <div style={{ fontSize: 10, color: T.faint, fontWeight: 600 }}>of {r.of}</div>
                    </td>
                    <td style={td}>
                      {q
                        ? <span title={q.note} style={{ fontSize: 10.5, fontWeight: 800, color: q.color,
                                background: q.bg, border: `1px solid ${q.color}33`, borderRadius: 6,
                                padding: "2px 7px", whiteSpace: "nowrap" }}>{q.short}</span>
                        : <span title={`${r.n} year${r.n === 1 ? "" : "s"} of ${cropLabel} — ${YA_MIN_CV_YEARS} are needed before stability can be called. Performance and rank stand.`}
                                style={{ fontSize: 10.5, fontWeight: 700, color: T.faint,
                                         background: T.surfaceAlt, border: `1px solid ${T.line}`, borderRadius: 6,
                                         padding: "2px 7px", whiteSpace: "nowrap" }}>
                            {r.index >= 1 ? "Above" : "Below"} · needs {YA_MIN_CV_YEARS}y
                          </span>}
                    </td>
                    {/* The reliability-adjusted figure, not the raw average — see
                        the shrinkage note in _jd.js. Raw is one hover away. */}
                    <td style={{ ...td, textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums",
                                 color: r.index >= 1 ? "#2f7d32" : "#8c3a3a" }}
                        title={r.raw == null ? "" :
                          `Averaged ${(r.raw * 100).toFixed(0)}% of the rest of the farm's ${cropLabel.toLowerCase()} over ${r.n} year${r.n === 1 ? "" : "s"}. ` +
                          `Shown at ${(r.index * 100).toFixed(0)}% because ${r.n} year${r.n === 1 ? "" : "s"} supports ${((r.reliability || 0) * 100).toFixed(0)}% of that claim — ` +
                          `on this book a single season swings a field far more than the ground does, so short histories are pulled toward the farm average.`}>
                      {(r.index * 100).toFixed(0)}%
                      {r.raw != null && Math.abs(r.raw - r.index) >= 0.03 && (
                        <div style={{ fontSize: 10.5, color: T.faint, fontWeight: 600 }}>
                          raw {(r.raw * 100).toFixed(0)}%
                        </div>
                      )}
                    </td>
                    {/* STABILITY — this crop's own CV, season divided out. The word
                        is the answer; the number is there for anyone who wants it. */}
                    <td style={{ ...td, textAlign: "right" }}
                        title={r.icv == null
                          ? `Needs ${YA_MIN_CV_YEARS} years of ${cropLabel.toLowerCase()} — two points give you the gap between them, not a spread.`
                          : `${cropLabel} index CV ${r.icv.toFixed(2)} over ${r.n} years — swing after the season is divided out`}>
                      <span style={{ fontWeight: 700, color: st.color, fontSize: 12 }}>{st.word}</span>
                      <div style={{ fontSize: 10, color: T.faint, fontVariantNumeric: "tabular-nums" }}>
                        {r.icv == null ? `${r.n}y` : `CV ${r.icv.toFixed(2)}`}
                      </div>
                    </td>
                    {isAll ? (
                      /* Each crop's own standing, so the overall rank can always
                         be taken apart into the crops it came from. */
                      <td style={{ ...td }}>
                        <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                          {Object.entries(r.crops).filter(([k]) => k !== YA_ALL)
                            .sort((a, b) => b[1].index - a[1].index)
                            .map(([ck, cc]) => (
                              <span key={ck} title={`${yaCropLabel(ck)}: ${(cc.index * 100).toFixed(0)}% over ${cc.n} year${cc.n === 1 ? "" : "s"}`}
                                    style={{ fontSize: 10, fontWeight: 700, borderRadius: 5, padding: "1px 5px",
                                             whiteSpace: "nowrap",
                                             color: cc.index >= 1 ? "#2f7d32" : "#8c3a3a",
                                             background: cc.index >= 1 ? "#e8f4e6" : "#f8e9e9" }}>
                                {yaCropShort(ck)} {(cc.index * 100).toFixed(0)}
                              </span>
                            ))}
                        </span>
                      </td>
                    ) : (
                      /* Raw swing in the field's own bushels — what you feel in the
                         bin. Carries the season in it, so it never sets a label. */
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: T.body }}
                          title={`${r.lo}–${r.hi} ${unit} over ${r.n} ${cropLabel.toLowerCase()} year(s), season included`}>
                        {r.rawCv == null ? "—" : `±${(r.rawCv * 100).toFixed(0)}%`}
                      </td>
                    )}
                    {!isAll && (
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {r.mean == null ? "—" : Math.round(r.mean)}
                        <span style={{ fontSize: 10, color: T.faint, fontWeight: 500 }}> {unit}</span>
                      </td>
                    )}
                    {!isAll && (
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                          title="10th-percentile year — the floor this field holds when the season goes against it">
                        {r.p10 == null ? <span style={{ color: T.faint }}>—</span> : (
                          <>
                            <span style={{ fontWeight: 700 }}>{Math.round(r.p10)}</span>
                            {r.depend != null && (
                              <div style={{ fontSize: 10, fontWeight: 700,
                                            color: r.depend >= 0.85 ? "#2f7d32" : r.depend >= 0.70 ? "#8a5e10" : "#8c3a3a" }}>
                                {(r.depend * 100).toFixed(0)}% of typical
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    )}
                    {/* A ⚠ marks a year scored against only 3-4 peer fields — the
                        comparison is real but thin, and that shouldn't be silent. */}
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                        title={`${r.firstYr}–${r.lastYr}${r.minPeers != null ? ` · thinnest peer group: ${r.minPeers} fields` : ""}`}>
                      {r.n}
                      {r.minPeers != null && r.minPeers < 5 && (
                        <span style={{ color: "#8a5e10", fontSize: 10, fontWeight: 800 }} title={`One year compared against only ${r.minPeers} peer fields`}> ⚠</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.acres.toFixed(1)}</td>
                  </tr>

                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {openRow && (
        <YaFieldCard r={openRow} list={rankedAll} crop={crop} cropLabel={cropLabel} isAll={isAll} unit={unit}
          farmName={!farmId && farmsWithData.length > 1 ? (nameById.get(openRow.farmer_id) || "") : ""}
          onClose={() => setOpen(null)} onPick={setOpen} isMobile={isMobile} />
      )}

      <div style={{ fontSize: 11, color: T.faint, marginTop: 10, lineHeight: 1.6, maxWidth: 820 }}>
        <b>Every number on this page is {cropLabel.toLowerCase()} only</b> — computed from {cropLabel.toLowerCase()} years,
        against {cropLabel.toLowerCase()} peers. Nothing is averaged across crops. Switch the tab and the whole
        page is recomputed; tap a field to see all of its crops side by side.
        <b> vs farm</b> = this field ÷ the operation's acre-weighted {cropLabel.toLowerCase()} average, same year,
        averaged over the field's {cropLabel.toLowerCase()} years. 100% is dead average, and above/below is cut at
        exactly 100%.
        <b> Rank</b> is that same index against every other {cropLabel.toLowerCase()} field
        {farmId ? " on this farm" : " in view (pick a farm to rank within one operation)"} — a standing over all
        the years each field has run, not last season's leaderboard.
        Both are <b>reliability-adjusted</b>: on this book a field's year-to-year swing is about 5× the real
        field-to-field difference in corn (7× soybeans, 9× wheat), so a two-year average is mostly luck. Each
        field is pulled toward the farm average by how little evidence it has — the raw average is on the row
        and in the tooltip. This is why a field can't top a 65-field list on two good seasons.
        A record only counts if it is a clean measurement of <b>one</b> field, measured against everything
        harvested off that field in a year (so a corn/bean split still reads as one field). That refuses
        slivers — a 0.1-acre soybean record at 149 bu had put three Howlett fields on top of the list with
        "typical" yields near 99 bu — records that span beyond the field, where the yield looks perfectly
        reasonable but is two pieces of ground blended, and yields outside a physical range for the crop.
        About 11% of records; click the excluded count above to see exactly what was refused and why.
        <b> Stability</b> is the swing after the season is divided out. That division is the whole trick:
        on this book the season alone moves a farm's entire corn average by 14% and its wheat by 24%,
        which would otherwise drown the field signal. Steady is a CV at or under {YA_HIGH_CV.toFixed(2)}.
        <b> Swing</b> is the raw, unadjusted move in the field's own {unit} — what you actually feel in the bin,
        season included. <b>Bad year</b> is the 10th-percentile yield and the share of typical it holds.
        A <b>⚠</b> on the year count means one of those years was scored against fewer than 5 peer fields.
        Fields need {YA_MIN_YEARS}+ harvest seasons, {YA_MIN_ACRES}+ acres and {YA_MIN_PEERS}+ same-crop peers to
        appear at all, {YA_MIN_CROP_YEARS}+ years of a crop to be ranked in it, and {YA_MIN_CV_YEARS}+ before
        stability is claimed.
        Dollars per acre and trend were removed: one price and one cost per crop made every $/ac gap
        inside a farm just the yield gap in a dollar sign, and a straight line through 3–8 points invited
        conclusions the sample can't carry.
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// THE SAME GROUND INSURED TWICE IN ONE CROP YEAR — the working list.
//
// Kevin, 2026-10-01: "catch a scenario where the farmer ripped up wheat without telling us
// and then planted to a spring crop". It lives on Crop Review because this page already
// reasons about a fall crop belonging to the NEXT crop year, already carries a per-lens crop
// year, and already has the reviewer sign-off a call list needs.
//
// ⚠ IT REPORTS A CONTRADICTION, NOT A VERDICT. Two acreage reports cannot both be right about
// one piece of ground; WHY is a phone call. The rule's own header carries the measurements.
function DoubleInsuredPanel({ dbl, nameById, open, onToggle, onSelect, onOpenField = null, isMobile }) {
  // ⚠ HOOKS BEFORE THE EARLY RETURN. The guard below is unconditional-looking but it is not:
  // a hook declared after it runs on some renders and not others, which React treats as a
  // changed hook order and throws on.
  //
  // Kevin, 2026-10-02: "Can we add filters to this so I could sort by PRF insured fields that
  // then get a spring insurance, and then a filter for wheat that becomes a spring crop."
  const [kindF, setKindF] = useState("all");      // all | prf_spring | fall_spring
  const [certF, setCertF] = useState("all");      // all | certain
  if (!dbl || !dbl.totals || !dbl.totals.cells) return null;
  const t = dbl.totals;
  const card = { background: "#fff8ec", border: "1px solid #e8d4a8", borderRadius: 10,
                 padding: isMobile ? 12 : 14, marginBottom: 14 };
  // ⚠ EVERY STATE THE RULE CAN PRODUCE HAS A BRANCH, AND THERE IS NO SENTENCE-BORROWING DEFAULT.
  // This was three branches over six states, so `rowCropHarvested` — the PRF confirmation, and
  // the most actionable thing this panel can say — fell through to the else and rendered as the
  // amber "no machine pass — unknown". The rule said a row crop was combined on insured forage
  // and the screen said we had no data about it. A silent default over a growing enum did that,
  // so an unmapped state now prints its own key: visibly wrong beats quietly reassuring.
  // ⚠ SHORT, BECAUSE THE MEANING IS EXPLAINED ONCE ABOVE THE TABLE AND NOT ON EVERY ROW.
  // These used to carry their own interpretation ("the PRF is right, the spring report is not"),
  // which is the same sentence on every row of that state and pushed the rows to three lines.
  const EV_SAY = {
    // ⚠ "combined" IS THE WRONG VERB FOR FORAGE — a combine cuts grain, a chopper takes
    // forage. Kevin, 2026-10-02: "forage isn't combined, it's harvested."
    rowCropHarvested: { c: "#a33", s: "row crop harvested" },
    forageHarvested: { c: "#a36a00", s: "forage harvested" },
    fallHarvested: { c: "#2d6a2d", s: "fall crop harvested" },
    otherOnly: { c: "#a33", s: "a machine here for another crop" },
    unnamedPass: { c: "#8a6d1f", s: "a pass, crop not named" },  // the export did not say
    unknown: { c: "#8a6d1f", s: "no machine pass" },
  };
  const ev = (f) => EV_SAY[f.evidence] || { c: "#8a6d1f", s: f.evidence };
  // ⚠ ONE FILTER EXPRESSION, READ BY THE TABLE AND BY THE COUNT ABOVE IT. Two copies is how a
  // header comes to claim a different number from the rows under it.
  const shown = dbl.findings.filter((f) =>
    (kindF === "all" || f.kind === kindF) &&
    (certF === "all" || f.overlapClass === "certain"));
  const KINDS = [["all", "Both"], ["prf_spring", "PRF → spring"], ["fall_spring", "Wheat → spring"]];
  const btn = (on) => ({ fontSize: 11.5, fontWeight: on ? 800 : 600, cursor: "pointer",
    color: on ? "#fff" : "#6a5420", background: on ? "#7a5c00" : "#fff",
    border: "1px solid #e8d4a8", borderRadius: 7, padding: "3px 9px" });
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, color: "#7a5c00", fontSize: 14 }}>
          {/* ⚠ A PRF FINDING IS NOT "a fall crop AND a spring crop". Every PRF cell was counted
              into a sentence that did not describe it — and PRF is the half an agent cannot see
              anywhere else in this app. */}
          ⚠ {t.cells} field{t.cells === 1 ? "" : "s"} on {t.farms} farm{t.farms === 1 ? "" : "s"}
          {" "}{t.prfSpring && t.fallSpring
            ? <>{"are insured twice over — "}{t.fallSpring} to a fall crop beside a spring crop,
                {" "}{t.prfSpring} to PRF beside a spring crop</>
            : t.prfSpring
              ? <>are insured to PRF <i>and</i> to a spring crop on the same ground</>
              : <>are insured to a fall crop AND a spring crop on more acres than the ground holds</>}
        </div>
        <button onClick={onToggle} style={{ fontSize: 12, fontWeight: 700, cursor: "pointer", color: "#7a5c00",
          background: "#fff", border: "1px solid #e8d4a8", borderRadius: 7, padding: "4px 11px" }}>
          {open ? "Hide" : "Show the list"}
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: "#6a5420", marginTop: 6, lineHeight: 1.55 }}>
        {/* ⚠ TWO LINES. This was five paragraphs explaining how a fall crop is drilled, what RMA
            practice 053 is and when PRF is sold — teaching, not reporting, on a page an agent
            opens to work a list. The mechanism is in the code comments, which is where it belongs.
            Kevin asked for this twice. */}
        <b>{t.acres.toLocaleString()} acres</b> claimed twice.
        {/* ⚠ WHAT IS KNOWN AND WHAT IS NOT, ON THE FACE OF IT. "88 findings" without "82 unknown"
            claims knowledge this tool does not have. */}
        {/* ⚠ THESE COUNTS MUST ADD UP TO THE HEADLINE AND THEY DID NOT. Three of the rule's six
            states were printed, so a cell with a confirmed row crop on forage — or a confirmed
            forage harvest — was inside the total and inside none of the lines beneath it. Driven
            off the state list now, so a state added to the rule appears here on its own.
            Shortened with the rest of the banner; this is the one place the states are explained,
            which is why the ROW labels are now two or three words. */}
        <div style={{ marginTop: 6 }}>
          {[["rowCropHarvested", "a row crop came off forage ground"],
            ["forageHarvested", "forage came off it — so the spring report is the wrong half"],
            ["otherOnly", "a machine was there for another crop"],
            ["fallHarvested", "the fall crop was harvested, so it was not ripped up"],
            ["unnamedPass", "a pass the export does not name"],
            ["unknown", "no machine pass — unknown, never a no"]]
            .filter(([key]) => t[key]).map(([key, say], i) => (
              <span key={key}>{i ? " · " : ""}<b>{t[key]}</b> {say}</span>
            ))}
          {/* ⚠ SAID ON THE FACE OF IT, because a list of 76 that is really a list of 59 trains
              somebody to stop reading it. A claim on the fall crop is the grower telling us,
              which is the opposite of the "without telling us" case this panel is looking for. */}
          {t.explainedByClaim ? (
            <div style={{ marginTop: 4, color: "#2d6a2d" }}>
              <b>{t.explainedByClaim}</b> have a claim on file for the fall crop and sort to the bottom.
              {" "}<b>{t.cells - t.explainedByClaim}</b> need a look.
            </div>
          ) : null}
        </div>
        {/* ⚠ THE 053 / PRF TUTORIAL PARAGRAPH WAS CUT. It explained what RMA practice 053 is and
            when PRF is sold — true, and not what a reader needs every time they open the page.
            Kevin asked for less text here twice. What SURVIVES is the only part that varies per
            run. The per-row "cannot follow a fall crop in one year" marker still appears where it
            applies, which is where it means something. */}
        <div style={{ marginTop: 4 }}>
          None is written as a double-crop (RMA practice 053).
        </div>
        {dbl.uncheckableCount ? (
          <div style={{ marginTop: 4, color: "#8a6d1f" }}>
            {dbl.uncheckableCount} more could not be checked — no boundary acreage on file for the field.
          </div>
        ) : null}
      </div>
      {open ? (
        <div style={{ overflowX: "auto", marginTop: 10 }}>
          {/* ⚠ THE FILTERS. Kevin, 2026-10-02 — PRF-then-spring and wheat-then-spring are two
              different conversations with the grower, so they are two different lists.
              "Definitely overlapped" is the second axis and it is NOT cosmetic: it is the
              difference between acres proven to sit on top of each other and acres that merely
              exceed the field by less than two sources routinely disagree by. */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#8a7a54", fontWeight: 700 }}>SHOW</span>
              {KINDS.map(([v, lab]) => (
                <button key={v} onClick={() => setKindF(v)} style={btn(kindF === v)}>{lab}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#8a7a54", fontWeight: 700 }}>OVERLAP</span>
              <button onClick={() => setCertF("all")} style={btn(certF === "all")}>Any</button>
              <button onClick={() => setCertF("certain")} style={btn(certF === "certain")}>
                Definitely overlapped
              </button>
            </div>
            <span style={{ fontSize: 11.5, color: "#6a5420" }}>
              {shown.length === dbl.findings.length
                ? <>all <b>{dbl.findings.length}</b></>
                : <>showing <b>{shown.length}</b> of {dbl.findings.length}</>}
            </span>
          </div>
          {/* ⚠ "Definitely overlapped" HIDES SOMETHING, so it says how many and why. A filter
              that silently shortens a list reads as a shorter list. */}
          {certF === "certain" && t.probable ? (
            <div style={{ fontSize: 11.5, color: "#8a6d1f", marginBottom: 8 }}>
              {t.probable} hidden: the two reports exceed the field by under {DI_OVERLAP_MIN_AC} ac
              <i> and</i> under {Math.round(DI_OVERLAP_MIN_SHARE * 100)}% of it, which is inside what a stated
              acreage report and a measured boundary routinely differ by.
            </div>
          ) : null}
          {!shown.length ? (
            <div style={{ fontSize: 12.5, color: "#6a5420", padding: "10px 2px" }}>
              Nothing in this book is {kindF === "prf_spring" ? "insured to PRF and then to a spring crop"
                : kindF === "fall_spring" ? "insured to a fall crop and then to a spring crop" : "double-insured"}
              {certF === "certain" ? " with a definite overlap" : ""} — the filter above is what emptied this,
              not the book.
            </div>
          ) : null}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ textAlign: "left", color: "#8a7a54", borderBottom: "1px solid #e8d4a8" }}>
              <th style={{ padding: "4px 6px" }}>Farm</th>
              <th style={{ padding: "4px 6px" }}>County</th>
              <th style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>Farm / tract / field</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Field ac</th>
              <th style={{ padding: "4px 6px" }}>Insured to</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Over by</th>
              <th style={{ padding: "4px 6px" }}>Machine data says</th>
            </tr></thead>
            <tbody>
              {shown.map((f) => {
                const e = ev(f);
                return (
                  <tr key={f.farmerId + f.cluId} style={{ borderBottom: "1px solid #f2ead8" }}>
                    <td style={{ padding: "4px 6px", fontWeight: 700, color: "#1a2e1a", cursor: onSelect ? "pointer" : "default" }}
                      onClick={() => onSelect && onSelect(f.farmerId)}>{nameById[f.farmerId] || "—"}</td>
                    <td style={{ padding: "4px 6px" }}>{f.county}</td>
                    {/* ⚠ THE FIELD IS THE LINK, not the farm. Clicking it opens the Field Mapper
                        on THAT boundary — the question a reader has here is "which ground is
                        this", and farm/tract/field is the only thing on the row that answers it.
                        Falls back to plain text when no handler is passed (the portal, and the
                        render harness), because a control that looks clickable and is not is
                        worse than none. */}
                    <td style={{ padding: "4px 6px", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                      {onOpenField && f.cluId ? (
                        <span onClick={() => onOpenField(f.farmerId, f.cluId)}
                          title="Open this field in the Field Mapper"
                          style={{ cursor: "pointer", color: "#1a4a7a", textDecoration: "underline",
                                   textDecorationStyle: "dotted", textUnderlineOffset: 2 }}>
                          {f.fsn} / {f.tract} / {f.fieldId}
                        </span>
                      ) : <>{f.fsn} / {f.tract} / {f.fieldId}</>}
                    </td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{f.cluAcres}</td>
                    <td style={{ padding: "4px 6px" }}>
                      {/* ⚠ A PRF CELL HAS NO FALL CROP, so the fall half rendered as a bare " 0 +",
                          and PRF usually states NO acres — which must not read as zero acres. */}
                      {/* ⚠ "PRF acres not stated" READ AS A MISSING VALUE AND IT IS NOT ONE.
                          PRF is rated per 0.25° GRID, not per field — it never carries a field
                          acreage, so flagging its absence implied a data gap that does not exist,
                          and the row then said it a second time underneath. Kevin: "some of the
                          wording doesnt make sense, PRF not stated?" A stated PRF acreage (the
                          acres_stated basis) still prints, because there it is a real figure. */}
                      {f.kind === "prf_spring"
                        ? <>PRF{f.prfAcres == null ? "" : " " + f.prfAcres}
                            {" + "}{f.springCrops.join("+")} {f.springAcres}</>
                        : <>{f.fallCrops.join("+")} {f.fallAcres} + {f.springCrops.join("+")} {f.springAcres}</>}
                      {/* The pair is context, never the reason it is listed — the acres are. */}
                      {/* ⚠ `springCanFollowFall` is NULL on a PRF cell and `!null` is true, so this
                          printed "cannot follow a fall crop in one year" under a finding that has no
                          fall crop in it. Tested explicitly against false, not for falsiness. */}
                      {f.springCanFollowFall === false ? (
                        <div style={{ fontSize: 10.5, color: "#a33", fontWeight: 600 }}>cannot follow a fall crop in one year</div>
                      ) : null}
                      {/* Why "Over by" equals the whole field on these — otherwise it reads as
                          arithmetic a reader cannot check. */}
                      {/* ⚠ A PRF CLAIM NEVER REACHES HERE and that is deliberate — PRF settles
                          on a rainfall INDEX whatever is standing on the ground, so it neither
                          evidences nor requires that the acres were in forage. See the rule. */}
                      {f.fallClaim ? (
                        <div style={{ fontSize: 10.5, color: "#2d6a2d", fontWeight: 600 }}>
                          {f.fallClaim.crops.join("+")} claim on file · {f.fallClaim.statuses.join("/")}
                          {f.fallClaim.amount > 0 ? " · " + fmt(f.fallClaim.amount) : ""}
                        </div>
                      ) : null}
                      {/* Was: "...· PRF stated no acres, so the acres at stake are the field
                          itself" — which repeated the line above it and explained an
                          implementation detail rather than the finding. The share IS the finding. */}
                      {f.prfBasis === "whole_field" ? (
                        <div style={{ fontSize: 10.5, color: "#a36a00", fontWeight: 600 }}>
                          {f.springCrops.join("+").toLowerCase()} covers {f.springShare}% of this field
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums",
                                 color: f.overlapClass === "certain" ? "#a33" : "#8a6d1f" }}>
                      {f.overAcres}
                      {/* ⚠ THE SOFT ONES ARE MARKED ON THE ROW, not only behind the filter. A
                          reader working the unfiltered list must be able to tell an overlap that
                          is proven from one that is inside ordinary measurement disagreement —
                          hiding that distinction behind a control nobody clicked is how a 0.3-acre
                          rounding artifact gets phoned to a grower as a double-insured field. */}
                      {f.overlapClass === "probable" ? (
                        <div style={{ fontWeight: 600, fontSize: 10.5, color: "#8a6d1f", whiteSpace: "nowrap" }}
                          title={f.overlapWhy}>within rounding</div>
                      ) : null}
                    </td>
                    <td style={{ padding: "4px 6px", color: e.c, fontWeight: 600 }}>
                      {e.s}{f.sawCrops.length ? <span style={{ fontWeight: 400, color: "#8a7a54" }}> ({f.sawCrops.join(", ")})</span> : null}
                      {/* ⚠ WHOSE MACHINE, WHERE IT IS NOT THIS FARM'S — and this is the half that
                          made a true finding look invented. Kevin checked HARKINS' John Deere
                          account for the alfalfa this panel cited and found nothing past 2020,
                          because the passes are GARY SWEDE FARMS' machine on Harkins' ground
                          (96% / 85% / 94% of the three CLUs, so not a sliver). The attribution is
                          by area overlap and is correct; the panel simply never said so, and the
                          obvious way to check it came up empty. */}
                      {/* Kevin, 2026-10-02: "Gary Swede does harvest for Harkins, so if that's
                          what you found, keep it that way." So it is CUSTOM WORK, and the earlier
                          wording — "on this farm's ground" — implied a neighbour's machine had
                          strayed. The operator is simply named, which is what makes the finding
                          checkable: Harkins' own John Deere account stops at 2020, so without
                          this line the obvious way to verify it comes up empty. */}
                      {f.sawByOthers && f.sawByOthers.length ? (
                        <div style={{ fontSize: 10.5, color: "#6a7a6a", fontWeight: 600 }}>
                          by {f.sawByOthers.map((id) => nameById[id] || "another operator").join(", ")}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
function CropReviewView({ farmers, onSelect, isMobile, onOpenField = null }) {
  // Reviewers who can sign off (Kevin Keenan / Mike Meisenzahl). reviewed_by stores
  // the initials so the Reviewed tab shows who signed.
  const REVIEWERS = [{ id: "KK", label: "KK" }, { id: "MM", label: "MM" }];

  // ⚠ A FALL CROP IS PLANTED THE AUTUMN BEFORE ITS CROP YEAR, so the two lenses
  // review DIFFERENT crop years at the same moment: spring is the crop in the
  // ground now, fall is the wheat drilled this autumn, which is the NEXT crop
  // year. Running both off one year would compare this season's spring acres
  // against last season's wheat and call the difference a swing.
  //
  // THE LENS AND THE CROP YEAR ARE NOW SEPARATE CONCEPTS. The season picks the
  // DEFAULT year (seasonCropYear, the rule above, in src/cropYear.js); the year
  // picker overrides it. Kevin, 2026-09-10: the page must stop being hard-wired
  // to "Spring 2026 / Fall 2027" — those are the right ACTIVE CAMPAIGNS today
  // and they are not the same thing as the only years reviewable.
  //
  // ⚠ THE PICK IS REMEMBERED PER LENS, NOT PER PAGE. One remembered year would
  // carry a fall pick onto the spring lens, where it is a year out — the exact
  // mixed-year failure this page exists to catch in the BOOK.
  const [season, setSeason] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search || "");
      const s = p.get("cr_season");
      if (SEASONS.includes(s)) return s;
    } catch { /* no URL access — fall through to the default lens */ }
    return "spring";
  });
  const isFallSeason = season === "fall";

  // The years this page can actually answer for, read from the DATA (a crop
  // year with policies but no keyed coverage is NOT offered — selecting it
  // renders an empty table that reads as a broken screen). null until the
  // catalogue has loaded, which is different from "no years exist".
  const [yearCat, setYearCat] = useState(null);   // { years:[], byYear:{} } | null
  const availableYears = yearCat ? yearCat.years : [];

  // A user pick, per lens. null = follow the season default.
  const [yearPick, setYearPick] = useState(() => {
    const seed = { spring: null, fall: null };
    try {
      const p = new URLSearchParams(window.location.search || "");
      const s = SEASONS.includes(p.get("cr_season")) ? p.get("cr_season") : "spring";
      const y = asCropYear(p.get("cr_year"));
      if (y != null) seed[s] = y;
    } catch { /* ignore */ }
    for (const s of SEASONS) if (seed[s] == null) seed[s] = readStoredYear("cropReview", s);
    return seed;
  });

  // ONE resolution, used by every read, every write, every label and the print
  // path — so no two of them can disagree about which year the page is on.
  const yearResolved = useMemo(() => resolveSelectedYear("cropReview", {
    season,
    bookCropYear: CURRENT_CROP_YEAR,
    available: availableYears,
    urlYear: null,                 // consumed once into yearPick above
    storedYear: yearPick[season],
  }), [season, yearPick, availableYears]);

  const REVIEW_YEAR = yearResolved.year;
  const REVIEW_PRIOR = priorCropYear(REVIEW_YEAR);
  // The season default, kept so the picker can mark which option is the active
  // campaign rather than making the agent remember which year that is.
  const SEASON_DEFAULT_YEAR = seasonCropYear(season, CURRENT_CROP_YEAR);

  // Selecting a year is a page-level navigation: it goes in the URL so a link
  // to "Crop Review, fall, CY2028" survives a refresh and can be sent to Mike.
  // A crop year names no customer, so it carries no farmer identity into the
  // address bar.
  function pickYear(y) {
    const n = asCropYear(y);
    setYearPick((p) => ({ ...p, [season]: n }));
    writeStoredYear("cropReview", season, n);
    try {
      const p = new URLSearchParams(window.location.search || "");
      p.set("cr_season", season);
      if (n == null) p.delete("cr_year"); else p.set("cr_year", String(n));
      const qs = p.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    } catch { /* a browser that refuses history still gets a working page */ }
  }
  function pickSeason(s) {
    setSeason(s);
    try {
      const p = new URLSearchParams(window.location.search || "");
      p.set("cr_season", s);
      const y = yearPick[s];
      if (y == null) p.delete("cr_year"); else p.set("cr_year", String(y));
      const qs = p.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    } catch { /* ignore */ }
  }
  const [tab, setTab] = useState("pending");        // 'pending' | 'reviewed'
  // The same ground insured twice in one crop year — see src/claims/doubleInsured.js.
  const [dbl, setDbl] = useState(null);
  const [dblOpen, setDblOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows26, setRows26] = useState([]);         // crop_coverage rows w/ farmer_id attached
  const [prod25, setProd25] = useState([]);         // production_history (REVIEW_PRIOR) — planted acres
  const [snap25, setSnap25] = useState([]);         // coverage_snapshots (REVIEW_PRIOR) — approved APH
  const [ppElig, setPpElig] = useState([]);         // pp_eligibility (max eligible acres) w/ farmer_id attached
  const [addedLand, setAddedLand] = useState([]);   // added_land_factors cache (per farmer/county)
  const [alBusy, setAlBusy] = useState(null);       // farmer_id actively computing added-land
  const [alPending, setAlPending] = useState(() => new Set()); // farmer_ids queued for auto-compute
  const [alNote, setAlNote] = useState({});         // farmer_id → status/error note after a compute
  const autoTriedRef = useRef(new Set());           // farmer_ids auto-attempted this session (no re-loop)
  const [signoffs, setSignoffs] = useState([]);
  const [nameById, setNameById] = useState({});
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [onlyChanges, setOnlyChanges] = useState(false);

  const N = (v) => (v == null || v === "") ? null : Number(v);
  const cty = (c) => normalizeCounty(c || "").toUpperCase().trim();
  // pp_eligibility labels processing beans "PROCESSING"; crop_coverage uses
  // "PROCESSING BEANS". Align them so the max-eligible join doesn't silently drop.
  const normCrop = (c) => { const s = (c || "").toUpperCase().trim(); return s === "PROCESSING" ? "PROCESSING BEANS" : s; };
  const ckey = (fid, crop, county) => `${fid}|${normCrop(crop)}|${cty(county)}`;
  // Corn is keyed into the AIP as two distinct records — grain (GSG, bushels) and
  // silage (GSS, tons). Their APHs are in different units and must never be
  // acre-blended, so the display groups by (farmer,crop,county,UNIT). Max-eligible
  // / PP eligibility stays crop-level (ckey) because RMA tracks PP eligibility per
  // crop, and the feed types the whole corn eligibility to grain — see below.
  const rowUnit = (r) => yieldUnit({ crop: r.crop, type: r.type });
  const bkey = (fid, crop, county, unit) => `${ckey(fid, crop, county)}|${unit}`;

  // ⚠ REFETCH ON THE LENS **AND ON THE YEAR**. The two seasons read DIFFERENT
  // crop years, and the year picker moves either of them independently — so a
  // mount-only effect would render one year's rows under another year's heading.
  useEffect(() => { if (REVIEW_YEAR != null) loadAll(); }, [season, REVIEW_YEAR]);

  // THE YEAR LIST COMES FROM THE DATA, NOT FROM ARITHMETIC ON TODAY.
  // A crop year is offered only where a SYNCED-AIP policy in that year actually
  // carries a keyed coverage row. CY2025 has 50 policies and zero coverage rows
  // (crop_coverage is delete-and-reinsert per sync, so prior years empty out and
  // live in coverage_snapshots instead) — offering it would be a fake zero-data
  // year that renders as a broken page.
  useEffect(() => {
    let dead = false;
    (async () => {
      const [pols, cov] = await Promise.all([
        sbFetch("policies", { select: "id,crop_year,aip" }).catch(() => []),
        sbFetch("crop_coverage", { select: "policy_id" }).catch(() => []),
      ]);
      if (dead) return;
      const SYNCED = new Set(["NAU", "RAIN_AND_HAIL"]);
      const yearOf = new Map();
      for (const p of pols || []) if (SYNCED.has(p.aip)) yearOf.set(p.id, p.crop_year);
      const withCoverage = new Set();
      for (const c of cov || []) { const y = yearOf.get(c.policy_id); if (y != null) withCoverage.add(Number(y)); }
      const years = availableCropYears(
        (pols || []).filter((p) => SYNCED.has(p.aip)), "crop_year", { require: withCoverage });
      setYearCat({ years });
    })();
    return () => { dead = true; };
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [farmerRows, policies, cc, prod, snap, pp,
             diFields, diGeo, diFtf, diConn, diInd] = await Promise.all([
        sbFetch("farmers", { select: "id,name" }).catch(() => []),
        // Every read below is scoped to ONE crop year. assertYearIdentity is the
        // guard that a coverage level, a FIPS code or a null never reaches a slot
        // that means a crop year — `Number(null)` is 0, which is finite.
        sbFetch("policies", { select: "id,farmer_id,aip,crop_year,policy_number,file_last_seen_at", filters: [`crop_year=eq.${REVIEW_YEAR}`] }).catch(() => []),
        // pp_acres = prevent-plant acres, AUTHORITATIVE from the AIP M13 feed
        // (P11 f[37]='P' units, written by m13-sync/nau-sync). Lives on the base
        // coverage row alongside `acres` (which is planted+PP, the SOI insured
        // total). NO heuristics — do NOT infer PP from policy_fields planting dates.
        // `practice_code` is here for findDoubleInsured (RMA's 053 "Following Another Crop",
        // which is what tells a DECLARED double-crop from an undeclared one). It rides on this
        // read rather than a second one: the panel first fetched crop_coverage AGAIN, in the
        // same Promise.all, at the same scope — 2,629 rows pulled twice per page load.
        sbFetch("crop_coverage", { select: "policy_id,county,crop,plan,practice,practice_code,type,acres,pp_acres,aph_yield,coverage_level,liability" }).catch(() => []),
        sbFetch("production_history", { select: "farmer_id,crop,county,acres,production,yield_per_acre,record_type,type", filters: [`crop_year=eq.${REVIEW_PRIOR}`] }).catch(() => []),
        // REVIEW_PRIOR-year approved APH lives in coverage_snapshots (the year-tagged
        // copy of last year's crop_coverage), recovered from the prior-year M13/
        // NAU file via the backfill `?snapshots=1` path. This is the real 10-yr
        // APH, NOT a single-season yield — so the YoY is true APH-vs-APH.
        sbFetch("coverage_snapshots", { select: "farmer_id,crop,county,acres,aph_yield,type", filters: [`crop_year=eq.${REVIEW_PRIOR}`] }).catch(() => []),
        // Max eligible acres for prevent plant = pp_eligibility.max_acres (the
        // greatest acres certified/insured for the crop in any of the 4 most
        // recent crop years — the AIP feed's raw 4-yr max, NOT added-land-adjusted).
        sbFetch("pp_eligibility", { select: "policy_id,county,crop,max_acres" }).catch(() => []),
        // ⚠ THE SAME GROUND INSURED TWICE. A fall crop is drilled the autumn BEFORE its crop
        // year, so a wheat CY2026 acreage report is filed in October 2025 and the corn CY2026
        // report in May 2026 — and if the wheat failed over winter and went to corn, the wheat
        // report is never revised. Both then claim the same acres.
        //
        // Every column below is one the rule READS (NEEDS_COLUMNS in doubleInsured.js), and
        // `practice_code` is the one that must not be dropped: without it every DECLARED
        // double-crop becomes a false finding and the list silently grows.
        sbFetch("policy_fields", { select: "farmer_id,clu_id,crop,crop_year,acres,county,fsn,tract,field_id",
          filters: [`crop_year=eq.${REVIEW_YEAR}`] }).catch(() => []),
        sbFetch("field_geometries", { select: "clu_id,acres" }).catch(() => []),
        // jd_operation_id rides along so only evidence-grade passes reach the rule — a gone or
        // duplicate pass is not proof a field was harvested (src/claims/jdEvidence.js).
        sbFetch("jd_field_ftf", { select: "clu_id,crop,metric,operation_date,jd_org_id,jd_operation_id", filters: ["metric=eq.yield"] })
          .then((rows) => jdEvidenceClient(rows || [])).then((ev) => ev.rows).catch(() => []),
        // ⚠ THREE COLUMNS ONLY — jd_connections also holds access_token / refresh_token, and
        // this is a page read. 29 rows; it exists solely to say whose machine ran a pass.
        sbFetch("jd_connections", { select: "farmer_id,jd_org_id,jd_org_name" }).catch(() => []),
        // Kevin, 2026-10-02: "Walton's way angus farm had a legit wheat tear up claim." His ask
        // was a grower who tore up wheat WITHOUT telling us, and a claim is telling us — so a
        // claim on the fall crop is a documented explanation and the cell stops being a lead.
        // ⚠ NOT scoped by crop year here: the rule keys a claim to the policy's OWN year, and
        // scoping the read as well would drop a claim stamped against a neighbouring year.
        sbFetch("indemnities", { select: "policy_id,crop_year,crop,county,status,indemnity_amount" }).catch(() => []),
      ]);
      // Added-land factors cached from prior computes (per farmer/county). Read-only
      // here; the "Check added land" button (Phase 2) recomputes on demand.
      const al = await sbFetch("added_land_factors", { filters: [`crop_year=eq.${REVIEW_YEAR}`] }).catch(() => []);
      setAddedLand(al || []);
      const nm = {}; farmerRows.forEach(f => { nm[f.id] = f.name; });
      setNameById(nm);
      // ⚠ RUN OVER THE WHOLE BOOK FOR THIS CROP YEAR, not the filtered roster. A CLU
      // double-claimed by a farm that is already reviewed is still double-claimed, and
      // scoping this to the pending list would hide exactly the ones somebody signed off.
      try {
        setDbl(findDoubleInsured({
          policyFields: diFields || [], geometries: diGeo || [], coverages: cc || [],
          policies: policies || [], machineRows: diFtf || [], indemnities: diInd || [],
          connections: diConn || [], cropYear: REVIEW_YEAR,
        }));
      } catch (e) {
        // A table a migration may not have created must not take the page down — the same
        // rule the FSA-426 and production panels follow. A null reads as 'not computed',
        // which the banner below renders as nothing at all rather than as 'none found'.
        setDbl(null);
      }
      // Only trust acres from the synced AIPs (R&H + NAU SFTP feeds) — those are
      // the "keyed into the AIP" insured acres this page reviews. Other AIPs
      // (e.g. AGRI_SOMPO) have no sync, so their crop_coverage came from manual
      // imports and shouldn't surface here yet.
      const SYNCED_AIPS = new Set(["NAU", "RAIN_AND_HAIL"]);
      const polFarmer = {}; policies.forEach(p => { if (SYNCED_AIPS.has(p.aip)) polFarmer[p.id] = p.farmer_id; });
      setRows26(cc.filter(c => polFarmer[c.policy_id]).map(c => ({ ...c, farmer_id: polFarmer[c.policy_id] })));
      setProd25(prod);
      setSnap25(snap);
      // A policy the AIP's file no longer names froze the day it stopped being
      // sent, so its 4-yr max is an older answer. Marked here, resolved in the
      // MAX below — see supersededPolicyIds() for why absence of a stamp can
      // never mean superseded, and why nothing changes until a sync has run.
      const deadPids = supersededPolicyIds(policies);
      setPpElig(pp.filter(p => polFarmer[p.policy_id]).map(p => ({ ...p, farmer_id: polFarmer[p.policy_id], dead: deadPids.has(p.policy_id) })));
      await reloadSignoffs();
    } finally { setLoading(false); }
  }
  async function reloadSignoffs() {
    // ⚠ THE REVIEWED YEAR, NOT THE BOOK YEAR. A fall sign-off reviews next
    // crop year; stamping it with the book year filed a CY2027 review as CY2026
    // and a 2026 spring sign-off would then have answered for it.
    const s = await sbFetch("farm_signoffs", { filters: [`crop_year=eq.${REVIEW_YEAR}`] }).catch(() => []);
    setSignoffs(s);
  }

  function toggleExpand(fid) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(fid) ? next.delete(fid) : next.add(fid);
      return next;
    });
  }
  async function signOff(fid, who) {
    setBusy(fid);
    try {
      await sbInsert("farm_signoffs", [{ farmer_id: fid, season, crop_year: assertYearIdentity(REVIEW_YEAR, YEAR_IDENTITY.CROP, "signOff"), reviewed_by: who }]);
      await reloadSignoffs();
    } catch (e) { alert("Could not save sign-off: " + e.message); }
    finally { setBusy(null); }
  }
  async function unSign(fid) {
    setBusy(fid);
    try {
      await sbDelete("farm_signoffs", `farmer_id=eq.${fid}&season=eq.${season}&crop_year=eq.${REVIEW_YEAR}`);
      await reloadSignoffs();
    } catch (e) { alert("Could not undo sign-off: " + e.message); }
    finally { setBusy(null); }
  }

  // ── Build per-farmer YoY comparison ────────────────────────────────────────
  const byFarmer = useMemo(() => {
    // 2025 PLANTED acres per (farmer,crop,county) from production_history (the
    // APH database — the only table that retains prior-year reported acres).
    // Dedup typed M13 vs untyped Excel mirror. NOTE: this source's yield is a
    // single-season actual, NOT APH — so we take acres from here only; the
    // 2025 APH comes from coverage_snapshots below (true APH-vs-APH).
    const aphG = {};
    prod25.forEach(r => {
      if (N(r.acres) == null) return;
      const k = bkey(r.farmer_id, r.crop, r.county, rowUnit(r));
      (aphG[k] || (aphG[k] = { typed: [], untyped: [] }))[r.record_type ? "typed" : "untyped"].push(r);
    });
    const planted25 = {};
    Object.entries(aphG).forEach(([k, g]) => {
      const use = g.typed.length ? g.typed : g.untyped;
      planted25[k] = use.reduce((s, r) => s + N(r.acres), 0);
    });

    // 2025 APPROVED APH per (farmer,crop,county): acre-weighted across base rows
    // of last year's coverage snapshot — identical math to the 2026 cov below,
    // so the two APH columns are computed the same way and compare apples-to-
    // apples. Snapshots carry no untyped Excel mirror, so no dedup is needed.
    const aph25G = {};
    snap25.forEach(r => {
      const y = N(r.aph_yield);
      if (y == null) return;                // overlay layers (SCO/ECO/ARPI) carry no APH — skip
      const a = N(r.acres);
      const k = bkey(r.farmer_id, r.crop, r.county, rowUnit(r));
      const c = aph25G[k] || (aph25G[k] = { ywNum: 0, ywDen: 0, ySum: 0, yN: 0 });
      c.ySum += y; c.yN += 1;                              // simple-avg fallback
      if (a != null) { c.ywNum += y * a; c.ywDen += a; }   // acre-weighted when acres present
    });
    const aph25 = {};
    // Acre-weight when we have planted acres; else fall back to the simple
    // average of the approved yields. Rotation OFF-YEAR crops carry a valid
    // approved APH but zero planted acres (so no weight) — e.g. a corn-year
    // farmer's soybean APH. Without this fallback they'd read blank / "NEW".
    Object.entries(aph25G).forEach(([k, c]) => {
      aph25[k] = c.ywDen > 0 ? c.ywNum / c.ywDen : (c.yN > 0 ? c.ySum / c.yN : null);
    });

    // 2026 coverage per (farmer,crop,county): base rows (acres!=null) for acres/yield/
    // guarantee; ALL plan rows summed for total liability
    // CURRENT-year coverage per (farmer,crop,county). pp = AUTHORITATIVE prevent-
    // plant acres from crop_coverage.pp_acres (AIP M13 P11 f[37]='P'); it sits on
    // the base row, so summing across plan rows yields the coverage PP. NO heuristics.
    const cov = {};
    rows26.forEach(r => {
      const k = bkey(r.farmer_id, r.crop, r.county, rowUnit(r));
      const c = cov[k] || (cov[k] = { acres: 0, pp: 0, hasPP: false, ywNum: 0, ywDen: 0, gNum: 0, liab: 0, hasBase: false });
      const liab = N(r.liability); if (liab != null) c.liab += liab;
      const pp = N(r.pp_acres); if (pp != null) { c.pp += pp; c.hasPP = true; }
      const a = N(r.acres), y = N(r.aph_yield), cl = N(r.coverage_level);
      if (a != null) {
        c.hasBase = true; c.acres += a;
        if (y != null) { c.ywNum += y * a; c.ywDen += a; }
        if (y != null && cl != null) c.gNum += y * cl * a;
      }
    });

    // Max eligible acres per (farmer,crop,county): sum max_acres across the
    // practice/type rows (distinct ground per practice, so summing is the crop-
    // county eligibility). This is the raw 4-yr max from the AIP feed — added-land
    // adjustment (Phase 2, from the FSA-578 footprint) sits on top of this.
    // Sum ACROSS practice/type rows WITHIN a policy — those are distinct ground.
    // Then take the MAX across POLICIES, never the sum. A grower carrying two
    // policies on the same county+crop has ONE prevent-plant eligibility, not two:
    // Rich Harvest holds R&H MP-0932654 and NAU NY-944-6063809-26, each with a
    // Livingston corn row of 622.6 and an Ontario row of 10.2, and summing
    // reported double the acres he is actually eligible to prevent-plant.
    const eligByPolicy = {};
    ppElig.forEach(p => {
      const m = N(p.max_acres); if (m == null) return;
      const k = ckey(p.farmer_id, p.crop, p.county);
      const pk = `${k}||${p.policy_id || ""}`;
      const g = eligByPolicy[pk] || (eligByPolicy[pk] = { acres: 0, dead: !!p.dead });
      g.acres += m;
    });
    // A SUPERSEDED POLICY MUST NOT WIN THE MAX. Shipley's replaced Bedford corn
    // policy carries 95.8 eligible acres against the live policy's 82.09, and a
    // plain Math.max took it — 13.71 acres of eligibility he does not have. Live
    // outranks superseded whatever the acres say; between two rows of the same
    // standing the bigger eligibility still wins, exactly as before.
    //
    // A superseded row is only set aside where a LIVE row answers for the same
    // crop and county. 14 of the 15 affected cells book-wide have no live
    // counterpart at all, so dropping them outright would blank a line rather
    // than correct it. Kept identical to the Claim Manager copy of this rule.
    const bestElig = {};
    Object.entries(eligByPolicy).forEach(([pk, g]) => {
      const k = pk.slice(0, pk.lastIndexOf("||"));
      const cur = bestElig[k];
      if (!cur) { bestElig[k] = g; return; }
      if (cur.dead && !g.dead) { bestElig[k] = g; return; }
      if (!cur.dead && g.dead) return;
      if (g.acres > cur.acres) bestElig[k] = g;
    });
    const maxElig = {};
    Object.entries(bestElig).forEach(([k, g]) => { maxElig[k] = g.acres; });

    // One draft line per (farmer,crop,county,UNIT) bucket — grain and silage corn
    // land as separate lines, each with its own APH/acres in its own unit, so a
    // 203 bu grain yield is never acre-blended with a 20 ton silage yield.
    const draft = {};     // fid → [lines]
    const cropRoll = {};  // ckey → { acres26, pp, maxElig, lines[] } (crop-level PP eligibility)
    new Set([...Object.keys(planted25), ...Object.keys(aph25), ...Object.keys(cov)]).forEach(k => {
      const [fid, crop, county, unit] = k.split("|");
      const c26 = cov[k];
      const acres25 = planted25[k] != null ? planted25[k] : null;
      const acres26 = (c26 && c26.hasBase) ? c26.acres : null;
      const yld25 = aph25[k] != null ? aph25[k] : null;
      const yld26 = (c26 && c26.ywDen > 0) ? c26.ywNum / c26.ywDen : null;
      const guar26 = (c26 && c26.acres > 0 && c26.gNum > 0) ? c26.gNum / c26.acres : null;
      const liab26 = c26 ? c26.liab : null;
      const ppAcres = (c26 && c26.hasPP && c26.pp > 0) ? c26.pp : null;
      // crop_coverage.acres (acres26) is the SOI INSURED acres = planted + PP.
      // pp_acres is the authoritative prevent-plant portion (AIP M13 P11 f[37]='P'),
      // so the genuinely-planted portion is Total − PP.
      const planted26 = acres26 != null ? Math.max(0, acres26 - (ppAcres || 0)) : null;
      const dAcres = (acres25 != null && acres26 != null) ? acres26 - acres25 : null;
      let flag = null;
      // "new" = no prior-year acres AND no prior-year APH. A crop with a 2025
      // approved APH (yld25) but no 2025 planted acres is a rotation RETURN
      // (e.g. corn-year farmer back to soybeans), not new business — don't flag
      // it NEW just because last year was its off-year. (Silage keyed for the
      // first time this year correctly reads NEW: no prior acres, no prior APH.)
      if (acres25 == null && acres26 != null) flag = (yld25 == null) ? "new" : null;
      else if (acres25 != null && acres26 == null) flag = "dropped";
      else if (dAcres != null && Math.abs(dAcres) >= 10 && acres25 > 0 && Math.abs(dAcres / acres25) >= 0.15) flag = "swing";
      const isSpring = !FALL_CROPS_SET.has(crop) && !PERENNIAL_CROPS_SET.has(crop);
      // Perennials (grapes, orchards) belong to NEITHER lens — they are not
      // reviewed here at all, and folding them into fall would put an orchard on
      // a wheat acreage list.
      const isFall = FALL_CROPS_SET.has(crop) && !PERENNIAL_CROPS_SET.has(crop);
      // Silage = the ton-unit corn/sorghum bucket (GSS). Processing beans & grapes
      // are also ton-unit but are their own crops, so they aren't "silage".
      const silage = unit === "ton" && normCrop(crop) !== "PROCESSING BEANS" && normCrop(crop) !== "GRAPES";
      const line = {
        crop, county, unit, silage, isSpring, isFall,
        acres25, acres26, planted26, yld25, yld26, guar26, liab26, dAcres, ppAcres, flag,
        maxEligible: null, overEligible: false, cropAcres26: null,
      };
      (draft[fid] || (draft[fid] = [])).push(line);
      const rk = ckey(fid, crop, county);
      const r = cropRoll[rk] || (cropRoll[rk] = { acres26: 0, pp: 0, maxElig: maxElig[rk] != null ? maxElig[rk] : null, lines: [] });
      if (acres26 != null) r.acres26 += acres26;
      if (ppAcres != null) r.pp += ppAcres;
      r.lines.push(line);
    });

    // Max eligible / over-eligible are CROP-level (RMA tracks PP eligibility per
    // crop; grain + silage share one corn eligibility pool, which the feed types
    // to grain). Attach it to a single primary line per (crop,county) — the grain/
    // bushel line if present, else the largest-acre bucket — and compare the crop
    // TOTAL insured acres (cropAcres26), not the per-unit acres, against it.
    Object.values(cropRoll).forEach(r => {
      r.lines.forEach(l => { l.cropAcres26 = r.acres26; });
      // Prevent-plant eligibility is CROP-level but the lines are per unit, so it
      // has to hang on one of them. Use the line with the most 2026 acres, not the
      // bushel line: on a dairy running 2,400 ac of silage and 100 ac of grain the
      // eligibility belongs against the silage, and preferring "bu" left the silage
      // line blank — which reads as "no PP eligibility on corn silage".
      const byAcres = r.lines.slice().sort((a, b) =>
        (b.acres26 || 0) - (a.acres26 || 0) || (a.unit === "bu" ? -1 : 1));
      const primary = byAcres[0] || r.lines.find(l => l.unit === "bu") || r.lines[0];
      if (primary) {
        primary.maxEligible = r.maxElig;
        primary.overEligible = !!(r.maxElig != null && r.pp > 0 && r.acres26 > r.maxElig + 0.5);
      }
    });
    return draft;
  }, [rows26, prod25, snap25, ppElig]);

  const signedMap = useMemo(() => {
    const m = {};
    signoffs.forEach(s => { m[`${s.farmer_id}|${s.season}`] = s; });
    return m;
  }, [signoffs]);

  // Added-land factors grouped by farmer (a farm can span counties). Refreshed
  // after a compute.
  const factorByFarmer = useMemo(() => {
    const m = {};
    addedLand.forEach(r => { (m[r.farmer_id] || (m[r.farmer_id] = [])).push(r); });
    return m;
  }, [addedLand]);

  // For an over-eligible line, resolve it against the added-land factor for THAT
  // LINE'S COUNTY (RMA BP 17(e)(1)(i)(B) — eligible acres + the ratio are per county;
  // cropland = 578 tract "Cropland" in that county, this year ÷ last year):
  //  - factor > 1  → new PP eligibility = max_acres × factor; resolved if planted+PP fits
  //  - factor ≤ 1  → county cropland didn't grow → factor does NOT apply → still over
  //  - missing_prior → couldn't find/parse a prior-year 578 for that county → flag
  //  - no row for the county → the 578s carry no cropland for this county → flag
  const alInfo = (fid, line) => {
    if (!line.overEligible) return null;
    const rows = factorByFarmer[fid] || [];
    if (!rows.length) return { checked: false };
    const al = rows.find(r => cty(r.county) === cty(line.county));
    if (!al) return { checked: true, resolved: false, noCounty: true };
    // Incomplete 578 (cropland below the insured floor for that county/year) → NEVER
    // show a ratio; the data is provably wrong. Say "incomplete" and stop.
    if (al.status === "incomplete_prior") return { checked: true, resolved: false, incomplete: true, incYear: REVIEW_PRIOR, al };
    if (al.status === "incomplete_current") return { checked: true, resolved: false, incomplete: true, incYear: REVIEW_YEAR, al };
    if (al.status === "missing_prior") return { checked: true, resolved: false, missingPrior: true, al };
    if (al.status === "missing_current") return { checked: true, resolved: false, missingCurrent: true, al };
    const f = N(al.factor);
    // Eligibility compares against the crop TOTAL insured acres (grain + silage),
    // since PP eligibility is a single crop-level pool — cropAcres26 on the primary
    // line. Falls back to the line's own acres for single-unit crops.
    const a26 = line.cropAcres26 != null ? line.cropAcres26 : line.acres26;
    if (f != null && f > 1 && line.maxEligible != null) {
      const adjusted = line.maxEligible * f;
      return { checked: true, resolved: a26 != null && a26 <= adjusted + 0.5, adjusted, factor: f, al };
    }
    return { checked: true, resolved: false, factor: f, noIncrease: f != null && f <= 1, al };
  };

  // ROLLING PP eligible acres (RMA Handbook 26C(9) "Using another crop's eligible
  // acres"): when a crop's prevent-plant exceeds its own eligibility (even after any
  // added-land factor), the adjuster rolls the excess to ANOTHER crop in the SAME
  // county that still has remaining eligibility, paid at the lesser of the two crops'
  // PP rates. This shows what the adjuster should do before they see it. `line` is
  // an over-eligible line; returns null if added land already covered it.
  const rollInfo = (farm, line) => {
    const info = alInfo(farm.fid, line);
    if (info && (info.incomplete || info.missingPrior || info.missingCurrent || info.noCounty)) return null;
    const factor = info && info.factor && info.factor > 1 ? info.factor : 1;
    const ownAdj = line.maxEligible != null ? line.maxEligible * factor : 0;
    // Crop-total insured acres (grain + silage) — PP eligibility is one crop pool.
    const a26 = (line.cropAcres26 != null ? line.cropAcres26 : line.acres26) || 0;
    const excess = a26 - ownAdj;
    if (excess <= 0.5) return null;   // added land (or base) already covers it
    const targets = farm.lines
      .filter(l => cty(l.county) === cty(line.county) && l.crop !== line.crop && l.maxEligible != null && l.maxEligible > 0)
      .map(l => { const used = (l.cropAcres26 != null ? l.cropAcres26 : l.acres26) || 0; const adj = l.maxEligible * factor, rem = adj - used; return { crop: l.crop, adjusted: adj, used, remaining: rem }; })
      .filter(t => t.remaining > 0.5)
      .sort((a, b) => b.remaining - a.remaining);   // advisory order (adjuster uses closest PP payment)
    let left = excess;
    const rolls = [];
    for (const t of targets) { if (left <= 0.5) break; const take = Math.min(left, t.remaining); rolls.push({ ...t, take }); left -= take; }
    return { excess, ownAdj, factor, rolls, uninsurable: Math.max(0, left), resolved: left <= 0.5 };
  };

  // Phase 2: pull the farmer's prior + current-year FSA-578s, compute the per-county
  // added-land factor, cache it, then refresh the lookup. Admin-gated endpoint.
  async function checkAddedLand(fid) {
    setAlBusy(fid);
    setAlNote(n => ({ ...n, [fid]: null }));
    try {
      const r = await adminApiFetch(`/api/market?fn=added-land&action=compute`, {
        method: "POST", body: JSON.stringify({ farmer_id: fid, crop_year: assertYearIdentity(REVIEW_YEAR, YEAR_IDENTITY.CROP, "checkAddedLand") }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(typeof d.error === "string" ? d.error : `HTTP ${r.status}`);
      // ⚠ THE SAME YEAR THE PAGE LOADED. This re-read used the BOOK year while
      // loadAll() used the REVIEWED year, so on the fall lens pressing this
      // button swapped the added-land column to a different crop year with
      // nothing on screen to say so. Measured 2026-09-10: added_land_factors
      // held 10 rows, ALL CY2026, so the fall lens showed none until you
      // pressed it and then showed CY2026 under a CY2027 heading.
      const al = await sbFetch("added_land_factors", { filters: [`crop_year=eq.${REVIEW_YEAR}`] }).catch(() => []);
      setAddedLand(al || []);
      const diag = d.diagnostics || {};
      const summary = (d.counties || []).map(c =>
        c.status === "computed" ? `${c.county} ×${fmt(c.factor, 2)}`
        : c.status === "missing_prior" ? `${c.county} no ${REVIEW_PRIOR} 578`
        : `${c.county} ${c.status}`
      ).join(" · ") || "no 578 cropland parsed";
      const warn = diag.dropbox_note || ((diag.parse_errors || []).length ? `${diag.parse_errors.length} parse error(s)` : null);
      setAlNote(n => ({ ...n, [fid]: { ok: true, msg: summary, warn, priorCount: diag.prior_578_count } }));
    } catch (e) {
      setAlNote(n => ({ ...n, [fid]: { ok: false, msg: e.message } }));
    } finally {
      setAlBusy(null);
      setAlPending(p => { const n = new Set(p); n.delete(fid); return n; });
    }
  }

  // Pin the COMPLETE prior-year 578(s) for a farmer when the Dropbox auto-search
  // only turns up partial/fragment files. Uploads to the storage pin prefix the
  // compute reads as authoritative, then recomputes.
  async function uploadPinned578(fid, fileList) {
    const files = Array.from(fileList || []).filter(f => /\.pdf$/i.test(f.name));
    if (!files.length) return;
    setAlBusy(fid);
    setAlNote(n => ({ ...n, [fid]: { ok: true, msg: `uploading ${files.length} file(s)…` } }));
    try {
      for (const f of files) {
        const safe = f.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        await sbStorageUpload("farmer-documents", `added-land-pins/${fid}/${REVIEW_PRIOR}/${safe}`, f);
      }
      await checkAddedLand(fid);   // recompute using the pinned files
    } catch (e) {
      setAlNote(n => ({ ...n, [fid]: { ok: false, msg: `upload failed: ${e.message}` } }));
      setAlBusy(null);
    }
  }

  // Farms in scope = those with ≥1 keyed (acres-present) 2026 SPRING row crop.
  // Spring = annual field crop (not fall, not perennial). Perennials (grapes,
  // orchards) and unkeyed renewals are excluded so the list = farms whose 2026
  // insured acres have actually been keyed into the AIP.
  const seasonFarms = useMemo(() => {
    const list = [];
    Object.entries(byFarmer).forEach(([fid, lines]) => {
      const sl = lines.filter(l => (isFallSeason ? l.isFall : l.isSpring));
      if (!sl.some(l => l.acres26 != null)) return; // no keyed spring acres yet
      const flags = sl.filter(l => l.flag).length;
      const tot25 = sl.reduce((s, l) => s + (l.acres25 || 0), 0);
      const tot26 = sl.reduce((s, l) => s + (l.acres26 || 0), 0);
      const totLiab = sl.reduce((s, l) => s + (l.liab26 || 0), 0);
      const totPP = sl.reduce((s, l) => s + (l.ppAcres || 0), 0);
      const overCount = sl.filter(l => l.overEligible).length;
      list.push({ fid, name: nameById[fid] || "(unknown farm)", lines: sl, flags, tot25, tot26, totLiab, totPP, overCount,
        signed: signedMap[`${fid}|${season}`] || null });
    });
    return list;
  }, [byFarmer, season, isFallSeason, nameById, signedMap]);

  const pending = useMemo(() =>
    seasonFarms.filter(f => !f.signed).sort((a, b) => (b.flags - a.flags) || a.name.localeCompare(b.name)),
    [seasonFarms]);
  const reviewed = useMemo(() =>
    seasonFarms.filter(f => f.signed).sort((a, b) => a.name.localeCompare(b.name)),
    [seasonFarms]);

  const shown = tab === "pending" ? pending : reviewed;
  const totalFlags = pending.reduce((s, f) => s + f.flags, 0);

  // Auto-compute is DISABLED while the added-land formula is being finalized — it
  // was auto-parsing 578 PDFs (paid AI calls) on every open. Cached results still
  // display; the per-farmer 🔄 Recompute button still works for controlled testing.
  // Flip AUTO_ADDED_LAND back on once the formula is locked.
  const AUTO_ADDED_LAND = false;
  useEffect(() => {
    if (!AUTO_ADDED_LAND || loading) return;
    const todo = seasonFarms
      .filter(f => f.lines.some(l => l.overEligible)
        && !(factorByFarmer[f.fid] || []).length
        && !autoTriedRef.current.has(f.fid))
      .map(f => f.fid);
    if (!todo.length) return;
    todo.forEach(fid => autoTriedRef.current.add(fid));
    setAlPending(p => { const n = new Set(p); todo.forEach(fid => n.add(fid)); return n; });
    (async () => { for (const fid of todo) { await checkAddedLand(fid); } })();
  }, [loading, seasonFarms, factorByFarmer]);

  // ── styling helpers ────────────────────────────────────────────────────────
  const flagBadge = (flag) => {
    if (!flag) return null;
    const map = {
      new:     { t: "NEW",     bg: "#e3f3e0", c: "#2d6a2d" },
      dropped: { t: "DROPPED", bg: "#fbe3e3", c: "#a33" },
      swing:   { t: "Δ ACRES", bg: "#fdf3df", c: "#9a7b1a" },
    }[flag];
    return <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: map.bg, color: map.c, letterSpacing: "0.04em" }}>{map.t}</span>;
  };
  const deltaTxt = (d, dec = 0) => {
    if (d == null) return <span style={{ color: "#bbb" }}>—</span>;
    const pos = d > 0, zero = Math.abs(d) < (dec ? 0.05 : 0.5);
    return <span style={{ color: zero ? "#888" : pos ? "#2d6a2d" : "#b03030", fontWeight: 700 }}>{zero ? "±0" : (pos ? "+" : "") + fmt(d, dec)}</span>;
  };
  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} style={{
      padding: "8px 16px", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 800,
      background: tab === key ? "#1a2e1a" : "#e8efe8", color: tab === key ? "#8ad078" : "#4a6e4a",
    }}>{label}</button>
  );
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <h2 style={{ ...uiPageTitle(isMobile) }}>Crop Review</h2>
        {/* ⚠ THE LENS AND THE CROP YEAR ARE TWO CONTROLS, NOT ONE. The chip says
            WHICH CROP (spring row crops vs fall-planted) and the picker says
            WHICH YEAR. They were fused as "Spring 2026 / Fall 2027" — the right
            active campaigns in September 2026 and unreviewable in any other year.
            Each lens still OPENS on its own campaign year, because a fall crop is
            drilled the autumn before its crop year, so the two defaults differ by
            one on purpose. */}
        <div style={{ display: "flex", gap: 6 }}>
          {[["spring", "🌱 Spring Crops"], ["fall", "🌾 Fall / Wheat"]].map(([k, lbl]) => (
            <button key={k} onClick={() => pickSeason(k)}
              style={{ fontSize: 13, fontWeight: 800, cursor: "pointer",
                       color: season === k ? "#2d6a2d" : "#60735f",
                       background: season === k ? "#e3f3e0" : "#f4f7f4",
                       border: "1px solid " + (season === k ? "#bcdcbc" : "#e2e8e2"),
                       padding: "5px 12px", borderRadius: 16 }}>{lbl}</button>
          ))}
        </div>

        {/* THE CROP YEAR. Options come from the DATA (availableYears) — a year
            with no keyed coverage is not offered, because selecting it would
            render an empty table that reads as a broken page rather than as a
            year that has not been keyed yet. */}
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#4a6e4a", fontWeight: 700 }}>
          Crop Year
          <select
            value={REVIEW_YEAR == null ? "" : String(REVIEW_YEAR)}
            onChange={(e) => pickYear(e.target.value)}
            disabled={!yearCat}
            style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", padding: "5px 8px",
                     borderRadius: 8, border: "1px solid #cfe0cf", background: "#fff", cursor: "pointer" }}>
            {!yearCat && <option value="">…</option>}
            {yearCat && !availableYears.length && <option value="">no keyed year</option>}
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}{y === SEASON_DEFAULT_YEAR ? " (current)" : ""}</option>
            ))}
          </select>
        </label>

        {/* A year that is NOT this lens's active campaign is called out, so an
            agent who deep-linked into CY2028 or left the picker somewhere last
            week cannot mistake it for the year they are supposed to be keying. */}
        {REVIEW_YEAR != null && REVIEW_YEAR !== SEASON_DEFAULT_YEAR && (
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.03em", padding: "3px 9px",
                         borderRadius: 10, background: "#fdf3df", color: "#8a6a00", border: "1px solid #ecd9a8" }}>
            NOT THE CURRENT {isFallSeason ? "FALL" : "SPRING"} CAMPAIGN (CY{SEASON_DEFAULT_YEAR})
          </span>
        )}
        {yearResolved.rejected != null && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "#a33" }}>
            CY{yearResolved.rejected} has no keyed coverage — showing CY{REVIEW_YEAR}
          </span>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          {tabBtn("pending", `Pending (${pending.length})`)}
          {tabBtn("reviewed", `Reviewed (${reviewed.length})`)}
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#4a6e4a", fontWeight: 600, marginLeft: "auto", cursor: "pointer" }}>
          <input type="checkbox" checked={onlyChanges} onChange={e => setOnlyChanges(e.target.checked)} /> Only show changed rows
        </label>
      </div>

      <div style={{ fontSize: 12.5, color: "#5a7a5a", marginBottom: 16, lineHeight: 1.5, maxWidth: 820 }}>
        {/* On a phone the explanation folds behind a tap: eight lines of it put the
            first farm below the fold. The flagged count stays outside the fold. */}
        {(() => {
          const expl = (<>
            {isFallSeason ? "Fall-planted crops (wheat, barley, triticale, rye)" : "Spring row crops (corn, soybeans, dry beans, etc.)"} whose <b>{REVIEW_YEAR}</b> insured acres have been keyed into the AIP,
            compared against <b>{REVIEW_PRIOR}</b> planted acres &amp; <b>approved APH</b> (last year's coverage snapshot — the 10-yr APH, not a single-season
            yield), by crop &amp; county. Perennials (grapes, orchards) are excluded from both lenses; last-year liability isn’t retained, so <b>{REVIEW_YEAR}</b> liability/guarantee show as context.
          </>);
          return isMobile
            ? <details><summary style={{ cursor: "pointer", fontWeight: 700, color: "#2d4a2d" }}>What this compares</summary>{expl}</details>
            : expl;
        })()}
        {tab === "pending" && totalFlags > 0 && <> <b style={{ color: "#9a7b1a" }}>{totalFlags} flagged row{totalFlags === 1 ? "" : "s"}</b> across pending farms.</>}
      </div>

      {/* ⚠ THE SPRING LENS ONLY, AND THAT IS KEVIN'S CALL, NOT AN OVERSIGHT — 2026-10-02.
          He opened Fall / Wheat looking for it, saw nothing, and when offered the move said
          "no thats ok it can be on spring crops". So it stays here.
          The reason it reads naturally on this lens: the fall acreage report was filed last
          October and the SPRING one is what contradicts it, so the spring review is when a
          reader is holding both halves. `dbl` itself is season-independent — it is computed
          from fall + spring + PRF together — so this is a placement decision and not a data
          one, and moving it would show the identical panel on the other tab.
          Do not "fix" this to both lenses without asking him again. */}
      {!isFallSeason ? (
        <DoubleInsuredPanel dbl={dbl} nameById={nameById} open={dblOpen}
          onToggle={() => setDblOpen(o => !o)} onSelect={onSelect} onOpenField={onOpenField} isMobile={isMobile} />
      ) : null}

      {loading ? (
        <div style={{ color: "#4a6e4a", padding: 40 }}><UiSpinner />Loading…</div>
      ) : shown.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, padding: 40, textAlign: "center", color: "#60735f" }}>
          {/* ⚠ AN EMPTY FALL LIST IS THE NORMAL STATE UNTIL NOVEMBER, and an empty
              list that says nothing reads as "no wheat farms in the book". Measured
              2026-09-08: CY2027 carries 335 wheat coverages across 120 farms and ZERO
              with acres — fall acreage reporting closes Nov 15. So the reason is named
              rather than left to look like a gap. */}
          {tab === "pending"
            ? (isFallSeason
                ? `No fall farms awaiting review yet. Fall-planted acres are reported to the AIP by Nov 15, so ${REVIEW_YEAR} wheat appears here as it is keyed — an empty list now is the deadline, not a missing book.`
                : `No spring farms awaiting review. As ${REVIEW_YEAR} insured acres are keyed into the AIPs, farms appear here.`)
            : `No ${isFallSeason ? "fall" : "spring"} farms reviewed yet.`}
        </div>
      ) : (
        shown.map(farm => {
          const isOpen = expanded.has(farm.fid);
          const lines = (onlyChanges ? farm.lines.filter(l => l.flag) : farm.lines)
            .slice()
            // Group by county (all of a county's crops together), then by crop, then
            // unit so grain (bu) sorts above its silage (ton) line.
            .sort((a, b) => a.county.localeCompare(b.county) || a.crop.localeCompare(b.crop) || a.unit.localeCompare(b.unit));
          // Over-eligible lines and how many remain unresolved after any cached
          // added-land factor is applied (resolved = added land covers the overage).
          const overLines = farm.lines.filter(l => l.overEligible);
          // "Unresolved" = over eligibility AND not covered by added land AND not
          // fully covered by rolling to another crop (26C(9)). Those need attention.
          const unresolvedOver = overLines.filter(l => {
            const i = alInfo(farm.fid, l);
            if (i && i.resolved) return false;
            const rl = rollInfo(farm, l);
            return !(rl && rl.resolved);
          }).length;
          const note = alNote[farm.fid];
          const calculating = alBusy === farm.fid || alPending.has(farm.fid);
          const alRows = factorByFarmer[farm.fid] || [];
          return (
            <div key={farm.fid} style={{ background: "#fff", borderRadius: 12, marginBottom: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", overflow: "hidden",
              border: farm.flags > 0 && !farm.signed ? "1px solid #ecd9a8" : "1px solid #e6ece6" }}>
              {/* card header — click to expand/collapse the full table */}
              <div onClick={() => toggleExpand(farm.fid)} title={isOpen ? "Collapse" : "Expand to compare full data"}
                style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, padding: "12px 16px", background: "#f6faf6", borderBottom: isOpen ? "1px solid #eef2ee" : "none", cursor: "pointer" }}>
                <span style={{ color: "#60735f", fontSize: 12, width: 12, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .12s" }}>▶</span>
                <button onClick={(e) => { e.stopPropagation(); onSelect && onSelect(farm.fid); }} title="Open farm"
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", color: "#1a2e1a", fontSize: 15.5, fontWeight: 800 }}>
                  {farm.name}
                </button>
                <span style={{ fontSize: 11, color: "#60735f" }}>{farm.lines.length} crop{farm.lines.length === 1 ? "" : "s"}</span>
                {farm.flags > 0 && <span style={{ fontSize: 11, fontWeight: 800, color: "#9a7b1a", background: "#fdf3df", padding: "2px 9px", borderRadius: 10 }}>{farm.flags} flag{farm.flags === 1 ? "" : "s"}</span>}
                {overLines.length > 0 && (calculating
                  ? <span title="Pulling the prior + current FSA-578s and computing the added-land factor…" style={{ fontSize: 11, fontWeight: 800, color: "#7a5a1a", background: "#fdf3df", padding: "2px 9px", borderRadius: 10 }}>⏳ calculating added land…</span>
                  : unresolvedOver > 0
                  ? <span title="Prevent-plant acres exceed max eligible acres, and the added-land factor doesn't cover it — review before certifying" style={{ fontSize: 11, fontWeight: 800, color: "#b03030", background: "#fbe3e3", padding: "2px 9px", borderRadius: 10 }}>⚠️ {unresolvedOver} over eligible{overLines.length > unresolvedOver ? ` (of ${overLines.length})` : ""}</span>
                  : <span title="Every over-eligible line is covered — by the added-land factor and/or by rolling PP acres to another crop (Handbook 26C(9)). Expand for the breakdown." style={{ fontSize: 11, fontWeight: 800, color: "#2d6a2d", background: "#e3f3e0", padding: "2px 9px", borderRadius: 10 }}>✓ eligibility resolved</span>)}
                <div style={{ marginLeft: "auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, fontSize: 12, color: "#4a6e4a" }}>
                  <span>Acres <b style={{ color: "#1a2e1a" }}>{fmt(farm.tot25, 0)}</b> → <b style={{ color: "#1a2e1a" }}>{fmt(farm.tot26, 0)}</b> {deltaTxt(farm.tot26 - farm.tot25, 0)}</span>
                  {farm.totPP > 0 && <span style={{ color: "#9a5a1a", fontWeight: 700 }} title={`${REVIEW_YEAR} prevent-plant acres from the AIP M13 feed (P11 prevent-plant units) — both NAU and R&H`}>🌧️ PP <b>{fmt(farm.totPP, 0)}</b> ac</span>}
                  <span>Liability <b style={{ color: "#1a2e1a" }}>{fmtD0(farm.totLiab)}</b></span>
                  {farm.signed ? (
                    <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 11.5, color: "#2d6a2d", fontWeight: 800 }}>✓ {farm.signed.reviewed_by} · {new Date(farm.signed.reviewed_at).toLocaleDateString()}</span>
                      <button disabled={busy === farm.fid} onClick={() => unSign(farm.fid)}
                        style={{ padding: "6px 11px", border: "1px solid #cfd8cf", background: "#fff", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#666" }}>Undo</button>
                    </div>
                  ) : (
                    <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, color: "#60735f", fontWeight: 700 }}>Sign off:</span>
                      {REVIEWERS.map(r => (
                        <button key={r.id} disabled={busy === farm.fid} onClick={() => signOff(farm.fid, r.id)} title={`Sign off as ${r.id}`}
                          style={{ padding: "7px 13px", border: "none", background: "#2d6a2d", borderRadius: 7, cursor: "pointer", fontSize: 12.5, fontWeight: 800, color: "#fff", opacity: busy === farm.fid ? 0.6 : 1 }}>
                          ✓ {r.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* line table — only when expanded */}
              {isOpen && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 960 }}>
                  <thead>
                    <tr style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      <th style={{ textAlign: "left", padding: "8px 18px" }}>Crop</th>
                      <th style={{ textAlign: "left", padding: "8px 10px" }}>County</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }}>{REVIEW_PRIOR} ac</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }} title={`${REVIEW_YEAR} genuinely-planted acres = total insured acres − prevent-plant acres filed.`}>{REVIEW_YEAR} planted</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }} title={`${REVIEW_YEAR} prevent-plant acres — authoritative from the AIP M13 feed (crop_coverage.pp_acres, P11 prevent-plant units; both NAU and R&H). Reconciles to the FSA-578 "Pvt" column.`}>{REVIEW_YEAR} prevent planted</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }} title={`${REVIEW_YEAR} total insured acres (SOI figure — already includes reported prevent-plant).`}>{REVIEW_YEAR} total</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }} title={`Max eligible acres for prevent plant = the greatest acres certified/insured for this crop in any of the 4 most recent crop years (AIP pp_eligibility feed, summed across practices). Raw 4-yr max — NOT yet added-land adjusted.`}>Max eligible</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }}>Δ ac</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }}>{REVIEW_PRIOR} APH</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }}>{REVIEW_YEAR} APH</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }} title="Per-acre production guarantee (APH × coverage level), in each line's own unit — bu for grain, tons for silage.">{REVIEW_YEAR} guar/ac</th>
                      <th style={{ textAlign: "right", padding: "8px 18px" }}>{REVIEW_YEAR} liability</th>
                      <th style={{ textAlign: "left", padding: "8px 14px" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const info = l.overEligible ? alInfo(farm.fid, l) : null;
                      const resolved = !!(info && info.resolved);
                      const roll = l.overEligible && !resolved ? rollInfo(farm, l) : null;
                      return (
                      <tr key={i} style={{ borderTop: "1px solid #f0f4f0", background: l.flag === "swing" ? "#fffdf6" : l.flag === "dropped" ? "#fff8f8" : "transparent" }}>
                        <td style={{ padding: "8px 18px", fontWeight: 700, color: "#1a2e1a" }}>
                          {l.crop}
                          {/* Silage (GSS) and other non-bushel crops carry a unit tag so
                              the APH / guar columns read correctly (tons vs bushels). */}
                          {l.unit !== "bu" && (
                            <span style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#9a7b1a", letterSpacing: "0.02em" }}>
                              {l.silage ? `silage · ${l.unit}/ac` : `${l.unit}/ac`}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", color: "#4a6e4a" }}>{l.county}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>{l.acres25 == null ? <span style={{ color: "#ccc" }}>—</span> : fmt(l.acres25, 1)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>{l.planted26 == null ? <span style={{ color: "#ccc" }}>—</span> : fmt(l.planted26, 1)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: l.ppAcres ? 700 : 400, color: l.ppAcres ? "#9a5a1a" : "#ccc" }}>{l.ppAcres ? fmt(l.ppAcres, 1) : "—"}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: (l.overEligible && !resolved) ? "#b03030" : undefined }}>{l.acres26 == null ? <span style={{ color: "#ccc" }}>—</span> : fmt(l.acres26, 1)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", color: (l.overEligible && !resolved) ? "#b03030" : "#4a6e4a", fontWeight: l.overEligible ? 700 : 400 }} title={l.overEligible ? `Crop total planted + prevent-plant (${fmt(l.cropAcres26 != null ? l.cropAcres26 : l.acres26, 1)}) exceeds max eligible (${fmt(l.maxEligible, 1)}) by ${fmt((l.cropAcres26 != null ? l.cropAcres26 : l.acres26) - l.maxEligible, 1)} ac` : undefined}>
                          {l.maxEligible == null ? <span style={{ color: "#ccc" }} title="No PP eligibility loaded for this crop/county">—</span> : fmt(l.maxEligible, 1)}
                          {info && info.checked && info.incomplete && <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9a7b1a" }} title={`The ${info.incYear} FSA-578 is incomplete for ${l.county} — it shows ${fmt(info.al[info.incYear === REVIEW_PRIOR ? "cropland_prior" : "cropland_current"], 0)} ac cropland but we have ${fmt((info.incYear === REVIEW_PRIOR ? info.al.prior_crops : info.al.current_crops)?.insured, 0)} ac insured there. Won't show a ratio until the complete ${info.incYear} 578 is provided.`}>{info.incYear} 578 incomplete</div>}
                          {info && info.checked && info.missingPrior && <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9a7b1a" }} title={`Couldn't find/parse a ${REVIEW_PRIOR} FSA-578 covering ${l.county}, so the county cropland ratio can't be computed — verify manually`}>prior 578 not found</div>}
                          {info && info.checked && info.noCounty && <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9a7b1a" }} title={`The 578s found carry no cropland physically located in ${l.county} — verify the county / the right 578`}>no {l.county} cropland on 578</div>}
                          {info && info.checked && info.adjusted != null && (
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: resolved ? "#2d6a2d" : "#b03030", lineHeight: 1.35 }} title={`${l.county} added-land factor = ${fmt(info.al.cropland_current, 1)} ÷ ${fmt(info.al.cropland_prior, 1)} ac cropland available for planting`}>
                              <div>ratio ×{fmt(info.factor, 2)}</div>
                              <div>new elig {fmt(info.adjusted, 1)}</div>
                            </div>
                          )}
                          {info && info.checked && info.noIncrease && (
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9a7b1a", lineHeight: 1.35 }} title={`Cropland didn't grow (×${fmt(info.factor, 2)}) — the added-land factor doesn't apply, so eligibility stays at max eligible`}>
                              <div>×{fmt(info.factor, 2)} — no added land</div>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>{deltaTxt(l.dAcres, 1)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", color: "#4a6e4a" }}>{l.yld25 == null ? <span style={{ color: "#ccc" }}>—</span> : fmt(l.yld25, 1)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", color: "#4a6e4a" }}>{l.yld26 == null ? <span style={{ color: "#ccc" }}>—</span> : fmt(l.yld26, 1)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", color: "#4a6e4a" }}>{l.guar26 == null ? <span style={{ color: "#ccc" }}>—</span> : fmt(l.guar26, 1)}</td>
                        <td style={{ padding: "8px 18px", textAlign: "right" }}>{l.liab26 == null ? <span style={{ color: "#ccc" }}>—</span> : fmtD0(l.liab26)}</td>
                        <td style={{ padding: "8px 14px" }}>
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                            {flagBadge(l.flag)}
                            {l.overEligible && (
                              resolved
                                ? <span title={`Added-land factor ×${fmt(info.factor, 2)} covers the overage (adjusted eligible ${fmt(info.adjusted, 1)} ac)`} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: "#e3f3e0", color: "#2d6a2d", letterSpacing: "0.04em" }}>ADDED LAND ✓</span>
                                : (info && info.incomplete)
                                  ? <span title={`The ${info.incYear} FSA-578 is incomplete for ${l.county} (shows less cropland than we know is insured) — no ratio shown until the complete 578 is provided`} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: "#fdf3df", color: "#9a7b1a", letterSpacing: "0.04em" }}>578 INCOMPLETE</span>
                                : (info && (info.missingPrior || info.noCounty || info.missingCurrent))
                                  ? <span title={`Couldn't compute the ${l.county} cropland ratio from the 578s found — verify manually`} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: "#fdf3df", color: "#9a7b1a", letterSpacing: "0.04em" }}>VERIFY 578</span>
                                : (roll && roll.resolved)
                                  ? <span title={`Over eligibility by ${fmt(roll.excess, 1)} ac — rolls to ${roll.rolls.map(t => `${l.county} ${t.crop}`).join(", ")} (per Handbook 26C(9)). See resolution below.`} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: "#e3f3e0", color: "#2d6a2d", letterSpacing: "0.04em" }}>ROLLS ✓</span>
                                : (roll && !roll.resolved)
                                  ? <span title={`${fmt(roll.uninsurable, 1)} ac exceed all ${l.county} eligibility (after rolling) — uninsurable prevent-plant. See resolution below.`} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: "#fbe3e3", color: "#b03030", letterSpacing: "0.04em" }}>UNINSURABLE</span>
                                : (info && info.checked)
                                  ? <span title={`Over max eligible and no other ${l.county} crop has remaining eligibility to roll to — review before certifying.`} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: "#fbe3e3", color: "#b03030", letterSpacing: "0.04em" }}>STILL OVER</span>
                                  : <span title={`Prevent-plant + planted exceeds max eligible acres. Upload the ${REVIEW_PRIOR} 578 to check added land + rolling.`} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: "#fbe3e3", color: "#b03030", letterSpacing: "0.04em" }}>OVER ELIG</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                {overLines.length > 0 && (
                  <div style={{ padding: "10px 18px", borderTop: "1px solid #f0f4f0", background: "#fbfdfb" }}>
                    {calculating ? (
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#7a5a1a" }}>
                        ⏳ Calculating added land — pulling {REVIEW_PRIOR} + {REVIEW_YEAR} FSA-578s and summing the operation's plantable cropland…
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: alRows.length ? 6 : 0 }}>
                          <span style={{ fontSize: 11.5, fontWeight: 800, color: "#2d4a2d" }}>🌱 Added-land factor</span>
                          {!alRows.length && <span style={{ fontSize: 11.5, color: "#60735f" }}>not computed yet</span>}
                          <label title={`Upload ALL of this farmer's ${REVIEW_PRIOR} FSA-578 Producer Prints (every county / office). The tool computes the added-land factor from these — it does NOT pull from Dropbox.`}
                            style={{ marginLeft: "auto", padding: "5px 11px", border: "none", background: "#2d6a2d", borderRadius: 7, cursor: "pointer", fontSize: 11.5, fontWeight: 800, color: "#fff" }}>
                            📎 Upload {REVIEW_PRIOR} 578(s)
                            <input type="file" accept="application/pdf" multiple style={{ display: "none" }}
                              onChange={(e) => { uploadPinned578(farm.fid, e.target.files); e.target.value = ""; }} />
                          </label>
                          <button disabled={alBusy === farm.fid} onClick={() => checkAddedLand(farm.fid)} title="Recompute from the uploaded 578s + current-year data"
                            style={{ padding: "5px 11px", border: "1px solid #cfd8cf", background: "#fff", borderRadius: 7, cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#4a6e4a" }}>
                            🔄 Recompute
                          </button>
                        </div>
                        {/* Per-county added-land: one line per county the farmer's 578s cover. */}
                        {alRows.slice().sort((a, b) => String(a.county).localeCompare(String(b.county))).map((r, ri) => {
                          const f = Number(r.factor);
                          const grew = r.status === "computed" && f > 1;
                          const incomplete = r.status === "incomplete_prior" || r.status === "incomplete_current";
                          const incYr = r.status === "incomplete_prior" ? REVIEW_PRIOR : REVIEW_YEAR;
                          const insFloor = (r.status === "incomplete_prior" ? r.prior_crops : r.current_crops)?.insured;
                          const shown578 = r.status === "incomplete_prior" ? r.cropland_prior : r.cropland_current;
                          return (
                            <div key={ri} style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 2, color: grew ? "#2d6a2d" : (r.status === "computed" ? "#9a7b1a" : "#b03030") }}>
                              📍 {r.county}: {r.status === "computed"
                                ? `cropland ${fmt(r.cropland_prior, 0)} → ${fmt(r.cropland_current, 0)} ac · ratio ×${fmt(r.factor, 2)}${grew ? " (added land applies)" : " · no added land (didn't grow)"}`
                                : incomplete ? `⚠️ ${incYr} 578 INCOMPLETE — shows ${fmt(shown578, 0)} ac but ${fmt(insFloor, 0)} ac is insured here. No ratio until the complete ${incYr} 578 is provided.`
                                : r.status === "missing_prior" ? `📎 upload the ${REVIEW_PRIOR} 578(s) to compute (${fmt(r.cropland_current, 0)} ac this year)`
                                : `no ${REVIEW_YEAR} 578 cropland found`}
                            </div>
                          );
                        })}
                        {(() => {
                          const files = (alRows[0] || {}).source_files;
                          if (!Array.isArray(files) || !files.length) return null;
                          return (
                            <div style={{ fontSize: 10.5, paddingLeft: 16, lineHeight: 1.5, color: "#60735f", marginTop: 2 }}>
                              {files.slice().sort((a, b) => (a.year || 0) - (b.year || 0)).map((sf, si) => (
                                <div key={si} title="578 file used — tract Cropland (incl. prevent-plant + idle); tracts de-duplicated across a year's files so the same land is never double-counted">
                                  578 {sf.year}: <span style={{ fontWeight: 600, color: "#4a6e4a" }}>{sf.name}</span>{sf.tracts ? ` — ${sf.tracts} tracts` : ""}{Array.isArray(sf.counties) && sf.counties.length ? ` · ${sf.counties.map(c => `${c.county} ${fmt(c.cropland, 0)}${c.pp > 0 ? ` (${fmt(c.pp, 0)} PP)` : ""}`).join(", ")}` : ""}
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {/* Precise per-line resolution: filed → eligible (+added land) → over → roll (26C(9)). */}
                        <div style={{ marginTop: 8, borderTop: "1px dashed #dbe6db", paddingTop: 8 }}>
                          <div style={{ fontSize: 11.5, fontWeight: 800, color: "#2d4a2d", marginBottom: 5 }}>🌧️ Prevent-plant eligibility</div>
                          {overLines.map((l, oi) => {
                            const info = alInfo(farm.fid, l);
                            const roll = rollInfo(farm, l);
                            const filed = l.acres26 || 0;
                            const grewF = info && info.factor && info.factor > 1;
                            const adj = grewF ? info.adjusted : l.maxEligible;
                            const needs578 = info && (info.incomplete || info.missingPrior || info.missingCurrent || info.noCounty);
                            return (
                              <div key={oi} style={{ fontSize: 11, marginBottom: 7, background: "#fff", border: "1px solid #eef2ee", borderRadius: 7, padding: "7px 10px" }}>
                                <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 2 }}>{l.crop} · {l.county}</div>
                                <div style={{ color: "#4a6e4a", lineHeight: 1.6 }}>
                                  <b style={{ color: "#9a5a1a" }}>{fmt(filed, 1)} ac</b> prevent-plant filed · <b>{fmt(l.maxEligible, 1)} ac</b> max eligible (4-yr)
                                  {grewF && <> · added land <b>×{fmt(info.factor, 2)}</b> → <b style={{ color: "#2d6a2d" }}>{fmt(adj, 1)} ac eligible</b></>}
                                  {info && info.noIncrease && <> · added land ×{fmt(info.factor, 2)} (county cropland didn't grow — no increase)</>}
                                </div>
                                {needs578 ? (
                                  <div style={{ color: "#9a7b1a", fontWeight: 800, marginTop: 2 }}>📎 upload the {REVIEW_PRIOR} 578(s) to check added-land eligibility + rolling</div>
                                ) : info && info.resolved ? (
                                  <div style={{ color: "#2d6a2d", fontWeight: 800, marginTop: 2 }}>✓ within eligibility — added land covers it, no roll needed</div>
                                ) : roll ? (
                                  <div style={{ marginTop: 2 }}>
                                    <span style={{ color: "#b03030", fontWeight: 800 }}>{fmt(roll.excess, 1)} ac over eligibility</span>
                                    {roll.rolls.map((t, ti) => (
                                      <div key={ti} style={{ color: "#2d6a2d", fontWeight: 700, paddingLeft: 12 }}>↳ roll <b>{fmt(t.take, 1)} ac</b> to {l.county} <b>{t.crop}</b> <span style={{ fontWeight: 500, color: "#4a6e4a" }}>({fmt(t.remaining, 1)} ac eligibility available)</span></div>
                                    ))}
                                    {roll.resolved
                                      ? <div style={{ color: "#2d6a2d", fontWeight: 800, paddingLeft: 12 }}>✓ fully covered by rolling <span style={{ fontWeight: 500, color: "#60735f" }}>· paid at the rolled-to crop's PP rate (the lower of the two) — confirm with adjuster</span></div>
                                      : <div style={{ color: "#b03030", fontWeight: 800, paddingLeft: 12 }}>⚠️ {fmt(roll.uninsurable, 1)} ac exceed all {l.county} eligibility — uninsurable prevent-plant</div>}
                                  </div>
                                ) : (
                                  <div style={{ color: "#b03030", fontWeight: 800, marginTop: 2 }}>{fmt(filed - (l.maxEligible || 0), 1)} ac over — no other {l.county} crop has remaining eligibility to roll to</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {note && !calculating && (!note.ok || note.warn || note.priorCount === 0) && (
                      <div style={{ marginTop: 6, fontSize: 11.5, fontWeight: 600, color: note.ok ? "#9a7b1a" : "#b03030" }}>
                        {!note.ok && <>✗ {note.msg}</>}
                        {note.ok && note.priorCount === 0 && <>⚠️ no {REVIEW_PRIOR} FSA-578 found in Dropbox for this farmer — can't compute the ratio. Check the "{REVIEW_PRIOR} Spring 578s from FSA" folder / filename.</>}
                        {note.ok && note.warn && note.priorCount !== 0 && <>⚠️ {note.warn}</>}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// HISTORICAL DATA — Agency growth, acres, premium, and yield trends
// ─────────────────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
// HISTORICAL DATA — Tabbed shell (Yields + Prices Received)
// REPLACES the old HistoricalDataScreen function entirely.
// Paste this block over the original lines 14581-15033 in App.jsx.
// ═══════════════════════════════════════════════════════════════

function HistoricalDataScreen({ isMobile }) {
  const [activeTab, setActiveTab] = useState('yields'); // 'yields' | 'acres' | 'prices'

  const tabBtn = (key, label, icon) => (
    <button
      key={key}
      onClick={() => setActiveTab(key)}
      style={{
        padding: "10px 20px",
        borderRadius: "8px 8px 0 0",
        border: "1px solid #d0e0d0",
        borderBottom: activeTab === key ? "1px solid #fff" : "1px solid #d0e0d0",
        background: activeTab === key ? "#fff" : "#f0f4f0",
        color: activeTab === key ? "#1a2e1a" : "#60735f",
        fontSize: 14,
        fontWeight: activeTab === key ? 800 : 600,
        cursor: "pointer",
        marginRight: 4,
        position: "relative",
        bottom: -1,
      }}
    >
      {icon} {label}
    </button>
  );

  return (
    <div>
      <h1 style={{ ...uiPageTitle(isMobile), marginBottom: 12 }}>Historical Data</h1>

      <div style={{ borderBottom: "1px solid #d0e0d0", marginBottom: 16, display: "flex", flexWrap: "wrap" }}>
        {tabBtn('yields', 'Yields', '\ud83c\udf3d')}
        {tabBtn('acres', 'Acres Planted', '\ud83c\udf31')}
        {tabBtn('prices', 'Prices Received', '\ud83d\udcb0')}
      </div>

      {activeTab === 'yields' ? (
        <YieldsTab isMobile={isMobile} />
      ) : activeTab === 'acres' ? (
        <AcresPlantedTab isMobile={isMobile} />
      ) : (
        <PricesReceivedTab isMobile={isMobile} />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// YIELDS TAB — was HistoricalDataScreen body
// ═══════════════════════════════════════════════════════════════
function YieldsTab({ isMobile }) {
  const [prodData, setProdData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCrop, setSelectedCrop] = useState('CORN');
  // Default to NY only — the primary book footprint. Other states (PA, OH, etc.)
  // pull the cross-state NASS avg up unrealistically (IN at 204 bu vs NY at 140).
  const [selectedStates, setSelectedStates] = useState(new Set(['NY']));
  const [selectedCounties, setSelectedCounties] = useState(new Set());
  const [showStateFilter, setShowStateFilter] = useState(false);
  const [showCountyFilter, setShowCountyFilter] = useState(false);
  const [expandedYear, setExpandedYear] = useState(null);
  const [expandedCounty, setExpandedCounty] = useState(null);
  // SCO underlying MPCI level — drives the SCO column in the SCO/ECO trigger
  // estimator. 75% is the most common in this book; range options 50-85% in
  // 5pt steps mirror RMA's catastrophic-to-CAT coverage choices.
  const [scoUnderlying, setScoUnderlying] = useState(75);

  const CROPS = ['CORN', 'SOYBEANS', 'WHEAT'];
  const CROP_COLORS = { CORN: '#D97706', SOYBEANS: '#059669', WHEAT: '#B45309' };
  const [nassData, setNassData] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [prod, nass] = await Promise.all([
          sbFetch("production_history", {
            select: "crop_year,crop,county,state,acres,production,yield_per_acre,excluded_from_avg,farmer_name_raw",
            filters: ["source_file=eq.M13_auto", "crop=in.(CORN,SOYBEANS,WHEAT)", "crop_year=gte.2015"]
          }).catch(() => []),
          sbFetch("nass_data", {
            select: "state,county,crop,year,stat_type,value,fetched_at",
            filters: ["stat_type=in.(YIELD,YIELD_FORECAST)", "crop=in.(CORN,SOYBEANS,WHEAT)"]
          }).catch(() => []),
        ]);
        setProdData(prod || []);
        setNassData(nass || []);
      } catch (e) { console.error("Historical data load error:", e); }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}>Loading historical data...</div>;

  const toggleState = (st) => {
    const next = new Set(selectedStates);
    if (next.has(st)) next.delete(st); else next.add(st);
    setSelectedStates(next);
    if (next.size > 0) {
      const validCounties = new Set(prodData.filter(r => next.has((r.state||'').toUpperCase().trim())).map(r => (r.county||'').toUpperCase().trim()));
      setSelectedCounties(prev => { const n = new Set([...prev].filter(c => validCounties.has(c))); return n; });
    }
  };
  const toggleCounty = (co) => {
    const next = new Set(selectedCounties);
    if (next.has(co)) next.delete(co); else next.add(co);
    setSelectedCounties(next);
  };
  const clearFilters = () => { setSelectedStates(new Set()); setSelectedCounties(new Set()); };

  const allStates = [...new Set(prodData.map(r => (r.state||'').toUpperCase().trim()).filter(Boolean))].sort();
  const availableCounties = [...new Set(
    prodData
      .filter(r => selectedStates.size === 0 || selectedStates.has((r.state||'').toUpperCase().trim()))
      .map(r => (r.county||'').toUpperCase().trim())
      .filter(Boolean)
  )].sort();

  const filteredProd = prodData.filter(r => {
    if (selectedStates.size > 0 && !selectedStates.has((r.state||'').toUpperCase().trim())) return false;
    if (selectedCounties.size > 0 && !selectedCounties.has((r.county||'').toUpperCase().trim())) return false;
    return true;
  });

  const activeFilterCount = selectedStates.size + selectedCounties.size;
  const validRows = filteredProd.filter(r => r.crop === selectedCrop && !r.excluded_from_avg && isValidProdRow(r));

  // ── Yield by year ──
  const yieldByYear = {};
  validRows.forEach(r => {
    const yr = r.crop_year;
    if (!yieldByYear[yr]) yieldByYear[yr] = { prod: 0, acres: 0, farmers: new Set(), counties: new Set() };
    yieldByYear[yr].prod += Number(r.production || 0);
    yieldByYear[yr].acres += Number(r.acres || 0);
    if (r.farmer_name_raw) yieldByYear[yr].farmers.add(r.farmer_name_raw);
    if (r.county) yieldByYear[yr].counties.add(r.county);
  });
  const yieldYears = Object.keys(yieldByYear).map(Number).sort();
  const yieldRows = yieldYears.map(yr => {
    const d = yieldByYear[yr];
    return { year: yr, yield: d.acres > 0 ? d.prod / d.acres : null, acres: d.acres, farmers: d.farmers.size, counties: d.counties.size };
  });

  const allTimeAvg = (() => {
    const totalProd = yieldRows.reduce((s, r) => s + (r.yield != null ? r.yield * r.acres : 0), 0);
    const totalAc = yieldRows.reduce((s, r) => s + r.acres, 0);
    return totalAc > 0 ? totalProd / totalAc : null;
  })();

  const cc = CROP_COLORS[selectedCrop] || '#4a6e4a';
  const fmtY = (y) => y != null ? y.toFixed(1) : '\u2014';
  const fmtAc = (a) => a > 0 ? Math.round(a).toLocaleString() : '\u2014';
  const titleCase = (s) => (s||'').charAt(0).toUpperCase() + (s||'').slice(1).toLowerCase();

  // Dynamic label for the NASS state-avg column/legend \u2014 reflects whatever
  // state filter is active so "171.5" doesn't masquerade as a NY number.
  const stateAvgLabel = (() => {
    if (selectedStates.size === 0) return 'All States Avg';
    if (selectedStates.size === 1) return `${[...selectedStates][0]} Avg`;
    if (selectedStates.size <= 3) return `${[...selectedStates].sort().join(' + ')} Avg`;
    return `${selectedStates.size}-State Avg`;
  })();

  // ── NASS yield lookups ──
  const nassCropRows = nassData.filter(r => r.crop === selectedCrop && (r.stat_type === 'YIELD' || r.stat_type === 'YIELD_FORECAST'));
  const nassForecastYears = new Set(nassData.filter(r => r.crop === selectedCrop && r.stat_type === 'YIELD_FORECAST').map(r => r.year));
  // State-level avg by year (weighted across selected states, or all if none selected)
  const nassStateByYear = {};
  nassCropRows.filter(r => !r.county).forEach(r => {
    if (selectedStates.size > 0 && !selectedStates.has(r.state)) return;
    if (!nassStateByYear[r.year]) nassStateByYear[r.year] = { sum: 0, count: 0 };
    nassStateByYear[r.year].sum += r.value;
    nassStateByYear[r.year].count += 1;
  });
  const getNassStateYield = (yr) => {
    const d = nassStateByYear[yr];
    return d && d.count > 0 ? d.sum / d.count : null;
  };
  const isNassForecast = (yr) => nassForecastYears.has(yr);
  // County-level yield lookup (never forecast — county data is always final)
  // State required to disambiguate same-name counties (e.g. Livingston exists in NY, PA, and other states)
  const getNassCountyYield = (yr, state, county) => {
    const match = nassCropRows.find(r => 
      r.year === yr 
      && r.stat_type === 'YIELD' 
      && r.county 
      && r.state === state
      && r.county.toUpperCase() === (county||'').toUpperCase()
    );
    return match ? match.value : null;
  };

  // ── County breakdown for expanded year ──
  // Keyed by state|county so same-name counties across states (Livingston NY vs PA) don't collide.
  const getCountyBreakdown = (yr) => {
    const byKey = {};
    validRows.filter(r => r.crop_year === yr).forEach(r => {
      const state = (r.state || 'UNKNOWN').toUpperCase().trim();
      const county = (r.county || 'UNKNOWN').toUpperCase().trim();
      const key = `${state}|${county}`;
      if (!byKey[key]) byKey[key] = { state, county, prod: 0, acres: 0, farmers: new Set() };
      byKey[key].prod += Number(r.production || 0);
      byKey[key].acres += Number(r.acres || 0);
      if (r.farmer_name_raw) byKey[key].farmers.add(r.farmer_name_raw);
    });
    return Object.values(byKey).map(d => ({
      state: d.state,
      county: d.county,
      yield: d.acres > 0 ? d.prod / d.acres : null,
      acres: d.acres,
      farmers: d.farmers.size,
    })).sort((a, b) => (b.acres || 0) - (a.acres || 0));
  };

  // ── Farmer breakdown for expanded county+year ──
  // Requires state to disambiguate same-name counties
  const getFarmerBreakdown = (yr, state, county) => {
    const byFarmer = {};
    validRows.filter(r => r.crop_year === yr 
                       && (r.state || '').toUpperCase().trim() === state
                       && (r.county || '').toUpperCase().trim() === county).forEach(r => {
      const f = r.farmer_name_raw || 'UNKNOWN';
      if (!byFarmer[f]) byFarmer[f] = { prod: 0, acres: 0 };
      byFarmer[f].prod += Number(r.production || 0);
      byFarmer[f].acres += Number(r.acres || 0);
    });
    return Object.entries(byFarmer).map(([name, d]) => ({
      name, yield: d.acres > 0 ? d.prod / d.acres : null, acres: d.acres,
    })).sort((a, b) => (b.acres || 0) - (a.acres || 0));
  };

  // ── CSV Export ──
  const exportCSV = () => {
    const headers = ['Year','Your Book (bu/ac)',`${stateAvgLabel} (NASS)`,`vs ${stateAvgLabel.replace(' Avg','')}`,'Reported Acres','Farmers','Counties'];
    const summaryRows = [...yieldRows].reverse().map(r => {
      const nassYld = getNassStateYield(r.year);
      const delta = r.yield != null && nassYld != null ? r.yield - nassYld : null;
      return [r.year, r.yield != null ? r.yield.toFixed(1) : '', nassYld != null ? nassYld.toFixed(1) : '', delta != null ? delta.toFixed(1) : '', Math.round(r.acres || 0), r.farmers, r.counties];
    });
    const detailHeaders = ['Year','State','County','Farmer','Your Yield (bu/ac)','NASS County Yield','Acres'];
    const detailRows = [];
    yieldYears.forEach(yr => {
      getCountyBreakdown(yr).forEach(c => {
        const nassCty = getNassCountyYield(yr, c.state, c.county);
        detailRows.push([yr, c.state, c.county, '', c.yield != null ? c.yield.toFixed(1) : '', nassCty != null ? nassCty.toFixed(1) : '', Math.round(c.acres)]);
        getFarmerBreakdown(yr, c.state, c.county).forEach(f => {
          detailRows.push([yr, c.state, c.county, f.name, f.yield != null ? f.yield.toFixed(1) : '', '', Math.round(f.acres)]);
        });
      });
    });
    const esc = v => String(v).includes(',') ? `"${v}"` : v;
    let csv = `${selectedCrop} Yield History - Clover Ag\nFilters: ${activeFilterCount > 0 ? [...selectedStates].join('+') + (selectedCounties.size > 0 ? ' / ' + [...selectedCounties].join('+') : '') : 'All locations'}\n\n`;
    csv += 'SUMMARY\n' + headers.join(',') + '\n' + summaryRows.map(r => r.join(',')).join('\n');
    csv += '\n\nDETAIL BY COUNTY & FARMER\n' + detailHeaders.join(',') + '\n' + detailRows.map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `${selectedCrop.toLowerCase()}_yield_history_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Linear regression trendline ──
  const trendLine = (() => {
    const pts = yieldRows.filter(r => r.yield != null);
    if (pts.length < 2) return null;
    const n = pts.length;
    const sumX = pts.reduce((s, r) => s + r.year, 0), sumY = pts.reduce((s, r) => s + r.yield, 0);
    const sumXY = pts.reduce((s, r) => s + r.year * r.yield, 0), sumX2 = pts.reduce((s, r) => s + r.year * r.year, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    return { slope, intercept, predict: (yr) => slope * yr + intercept };
  })();
  const trendPerYear = trendLine ? trendLine.slope.toFixed(1) : null;

  // ── SVG Chart ──
  const chartW = 720, chartH = 300, padL = 55, padR = 30, padT = 30, padB = 40;
  const plotW = chartW - padL - padR, plotH = chartH - padT - padB;
  const yieldVals = yieldRows.map(r => r.yield).filter(Boolean);
  const trendVals = trendLine && yieldYears.length > 0 ? [trendLine.predict(yieldYears[0]), trendLine.predict(yieldYears[yieldYears.length-1])] : [];
  const nassYieldVals = yieldYears.map(yr => getNassStateYield(yr)).filter(Boolean);
  const allYVals = [...yieldVals, ...trendVals, ...nassYieldVals].filter(Boolean);
  const yMin = allYVals.length > 0 ? Math.floor(Math.min(...allYVals) / 10) * 10 - 10 : 0;
  const yMax = allYVals.length > 0 ? Math.ceil(Math.max(...allYVals) / 10) * 10 + 10 : 200;
  const minYr = yieldYears.length > 0 ? yieldYears[0] : 2015;
  const maxYr = yieldYears.length > 0 ? yieldYears[yieldYears.length - 1] : 2025;
  const yearRange = maxYr - minYr || 1;
  const xScale = (yr) => padL + ((yr - minYr) / yearRange) * plotW;
  const yScale = (v) => padT + plotH - ((v - yMin) / (yMax - yMin)) * plotH;
  const yTicks = [];
  const yStep = selectedCrop === 'SOYBEANS' ? 5 : selectedCrop === 'WHEAT' ? 10 : 20;
  for (let v = yMin; v <= yMax; v += yStep) yTicks.push(v);
  const linePath = yieldRows.filter(r => r.yield != null).map((r, i) => `${i === 0 ? 'M' : 'L'}${xScale(r.year).toFixed(1)},${yScale(r.yield).toFixed(1)}`).join(' ');
  // NASS state avg line
  const nassLinePts = yieldYears.map(yr => ({ yr, val: getNassStateYield(yr) })).filter(p => p.val != null);
  const nassLinePath = nassLinePts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(p.yr).toFixed(1)},${yScale(p.val).toFixed(1)}`).join(' ');
  const xAxisYears = []; for (let yr = minYr; yr <= maxYr; yr++) xAxisYears.push(yr);

  const filterDesc = activeFilterCount > 0
    ? [...selectedStates].join(', ') + (selectedCounties.size > 0 ? ' \u2014 ' + [...selectedCounties].map(titleCase).join(', ') : '')
    : 'All locations';

  const chip = (label, active, onClick) => (
    <button key={label} onClick={onClick} style={{
      padding: "4px 10px", borderRadius: 20, border: active ? `2px solid ${cc}` : "1px solid #d0e0d0",
      background: active ? cc : "#fff", color: active ? "#fff" : "#4a6e4a",
      fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        <button onClick={exportCSV} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #d0e0d0", background: "#f8faf8", color: "#4a6e4a", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
          {'\ud83d\udce5'} Export CSV
        </button>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 12, color: "#60735f" }}>
        Weighted average yields from M13 APH databases {'\u00b7'} 2015{'\u2013'}2025 {'\u00b7'} click any year row to drill into counties
        {activeFilterCount > 0 && <span style={{ color: cc, fontWeight: 600 }}> {'\u00b7'} {filterDesc}</span>}
        {latestTs(nassData, "fetched_at") && <> {'\u00b7'} <FreshAsOf date={latestTs(nassData, "fetched_at")} label="NASS survey" prefix="synced" style={{ display: "inline" }} /></>}
      </p>

      {/* Crop selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {CROPS.map(crop => (
          <button key={crop} onClick={() => { setSelectedCrop(crop); setExpandedYear(null); setExpandedCounty(null); }}
            style={{ flex: 1, padding: isMobile ? "10px 8px" : "10px 16px", borderRadius: 10, border: "none", cursor: "pointer",
              background: selectedCrop === crop ? CROP_COLORS[crop] : "#f0f4f0", whiteSpace: "nowrap", fontFamily: "inherit",
              color: selectedCrop === crop ? "#fff" : "#4a6e4a", fontSize: isMobile ? 13.5 : 14, fontWeight: 800 }}>
            {crop === 'CORN' ? '\ud83c\udf3d Corn' : crop === 'SOYBEANS' ? '\ud83e\uded8 Soybeans' : '\ud83c\udf3e Wheat'}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e0eae0", marginBottom: 20, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a" }}>{'\ud83d\udccd'} Filter by Location</span>
          {activeFilterCount > 0 && <button onClick={clearFilters} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #e0d0d0", background: "#fafafa", color: "#c0392b", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Clear all</button>}
        </div>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>State</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{allStates.map(st => chip(st, selectedStates.has(st), () => toggleState(st)))}</div>
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.08em" }}>County</span>
            <button onClick={() => setShowCountyFilter(p => !p)} style={{ background: "none", border: "none", color: "#60735f", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>
              {showCountyFilter ? '\u25b2 Hide' : `\u25bc Show (${availableCounties.length})`}
            </button>
            {selectedCounties.size > 0 && <span style={{ fontSize: 10, color: cc, fontWeight: 700 }}>{selectedCounties.size} selected</span>}
          </div>
          {!showCountyFilter && selectedCounties.size > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{[...selectedCounties].sort().map(co => chip(titleCase(co), true, () => toggleCounty(co)))}</div>}
          {showCountyFilter && <div style={{ display: "flex", gap: 5, flexWrap: "wrap", maxHeight: 200, overflowY: "auto", padding: "4px 0" }}>{availableCounties.map(co => chip(titleCase(co), selectedCounties.has(co), () => toggleCounty(co)))}</div>}
        </div>
      </div>

      {/* Summary stats */}
      {yieldRows.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 140px", padding: "12px 16px", background: "#fff", border: `1px solid ${cc}44`, borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: cc, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{yieldYears.length}-Year Avg</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: cc, fontVariantNumeric: "tabular-nums" }}>{fmtY(allTimeAvg)}</div>
            <div style={{ fontSize: 10, color: "#60735f" }}>bu/ac</div>
          </div>
          <div style={{ flex: "1 1 100px", padding: "12px 16px", background: "#fff", border: "1px solid #e0eae044", borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Latest ({yieldYears[yieldYears.length-1]})</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{fmtY(yieldRows[yieldRows.length-1]?.yield)}</div>
            <div style={{ fontSize: 10, color: "#60735f" }}>bu/ac</div>
          </div>
          <div style={{ flex: "1 1 100px", padding: "12px 16px", background: "#fff", border: "1px solid #e0eae044", borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Best Year</div>
            {(() => { const best = yieldRows.reduce((b, r) => (r.yield||0) > (b.yield||0) ? r : b, yieldRows[0]); return best ? <><div style={{ fontSize: 22, fontWeight: 800, color: "#059669", fontVariantNumeric: "tabular-nums" }}>{fmtY(best.yield)}</div><div style={{ fontSize: 10, color: "#60735f" }}>{best.year}</div></> : null; })()}
          </div>
          <div style={{ flex: "1 1 100px", padding: "12px 16px", background: "#fff", border: "1px solid #e0eae044", borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Worst Year</div>
            {(() => { const worst = yieldRows.reduce((w, r) => (r.yield||999) < (w.yield||999) ? r : w, yieldRows[0]); return worst ? <><div style={{ fontSize: 22, fontWeight: 800, color: "#DC2626", fontVariantNumeric: "tabular-nums" }}>{fmtY(worst.yield)}</div><div style={{ fontSize: 10, color: "#60735f" }}>{worst.year}</div></> : null; })()}
          </div>
        </div>
      )}

      {/* Yield Trend Chart */}
      {yieldRows.length > 1 && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e0eae0", marginBottom: 20, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#1a2e1a" }}>Yield Trend</span>
              {allTimeAvg != null && <span style={{ fontSize: 11, color: "#60735f", fontWeight: 400, marginLeft: 8 }}>avg: {allTimeAvg.toFixed(1)} bu/ac</span>}
            </div>
            {trendPerYear && <span style={{ fontSize: 11, fontWeight: 600, color: Number(trendPerYear) >= 0 ? "#059669" : "#DC2626" }}>Trend: {Number(trendPerYear) >= 0 ? '+' : ''}{trendPerYear} bu/ac per year</span>}
          </div>
          <div style={{ overflowX: "auto" }}>
            <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: "100%", maxWidth: 1000, display: "block" }}>
              {yTicks.map(v => (<g key={v}><line x1={padL} x2={chartW-padR} y1={yScale(v)} y2={yScale(v)} stroke="#e8ece8" strokeWidth="1" /><text x={padL-8} y={yScale(v)+4} textAnchor="end" fontSize="10" fill="#677167">{v}</text></g>))}
              {allTimeAvg != null && <line x1={padL} x2={chartW-padR} y1={yScale(allTimeAvg)} y2={yScale(allTimeAvg)} stroke={cc} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.3" />}
              {trendLine && <line x1={xScale(minYr)} y1={yScale(trendLine.predict(minYr))} x2={xScale(maxYr)} y2={yScale(trendLine.predict(maxYr))} stroke="#4a6e4a" strokeWidth="2" strokeDasharray="8,4" opacity="0.6" />}
              {/* NASS state avg line */}
              {nassLinePath && <path d={nassLinePath} fill="none" stroke="#6b9bd2" strokeWidth="2" strokeDasharray="6,3" opacity="0.7" />}
              {nassLinePts.map(p => (
                <circle key={`nass-${p.yr}`} cx={xScale(p.yr)} cy={yScale(p.val)} r="3"
                  fill={isNassForecast(p.yr) ? "transparent" : "#6b9bd2"}
                  stroke="#6b9bd2" strokeWidth={isNassForecast(p.yr) ? 2 : 1}
                  opacity="0.7" />
              ))}
              {nassLinePts.filter(p => isNassForecast(p.yr)).map(p => (
                <text key={`nass-f-${p.yr}`} x={xScale(p.yr)} y={yScale(p.val) - 8} textAnchor="middle" fontSize="8" fill="#6b9bd2" opacity="0.7">(f)</text>
              ))}
              <path d={linePath} fill="none" stroke={cc} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {yieldRows.filter(r => r.yield != null).map(r => {
                const thin = r.farmers < 50 && activeFilterCount === 0;
                const isExp = expandedYear === r.year;
                return (<g key={r.year} style={{ cursor: "pointer" }} onClick={() => { setExpandedYear(isExp ? null : r.year); setExpandedCounty(null); }}>
                  <circle cx={xScale(r.year)} cy={yScale(r.yield)} r={thin ? 4 : 5} fill={isExp ? "#fff" : (thin ? "#fff" : cc)} stroke={cc} strokeWidth={isExp ? 3 : (thin ? 1.5 : 2)} strokeDasharray={thin ? "2,2" : "none"} />
                  <text x={xScale(r.year)} y={yScale(r.yield)-12} textAnchor="middle" fontSize="10" fontWeight="700" fill={cc} opacity={thin ? 0.5 : 1} stroke="#fff" strokeWidth="3" paintOrder="stroke" strokeLinejoin="round">{r.yield.toFixed(1)}</text>
                </g>);
              })}
              {xAxisYears.map(yr => <text key={yr} x={xScale(yr)} y={chartH-8} textAnchor="middle" fontSize="10" fill="#60735f" fontWeight="600">{String(yr).slice(2)}</text>)}
              <text x={padL-8} y={padT-10} textAnchor="end" fontSize="9" fill="#aaa">bu/ac</text>
            </svg>
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5, color: "#60735f", flexWrap: "wrap" }}>
            <span><span style={{ display: "inline-block", width: 16, height: 2, background: cc, verticalAlign: "middle", marginRight: 4 }}></span> Your book</span>
            <span><span style={{ display: "inline-block", width: 16, height: 2, background: "#6b9bd2", verticalAlign: "middle", marginRight: 4, borderTop: "1px dashed #6b9bd2" }}></span> {stateAvgLabel} (NASS)</span>
            <span><span style={{ display: "inline-block", width: 16, height: 2, background: cc, opacity: 0.3, verticalAlign: "middle", marginRight: 4, borderTop: "1px dashed " + cc }}></span> Book avg</span>
            <span><span style={{ display: "inline-block", width: 16, height: 2, background: "#4a6e4a", opacity: 0.6, verticalAlign: "middle", marginRight: 4, borderTop: "1px dashed #4a6e4a" }}></span> Trendline</span>
          </div>
        </div>
      )}

      {/* Yield by Year Table with drill-down */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e0eae0", overflow: "hidden" }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #e0eae0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#1a2e1a" }}>Book Yield by Year</span>
            <span title={selectedStates.size === 1 && selectedStates.has('NY')
                ? "NY only — kept apples-to-apples with the NY NASS average. The Dashboard's Actual Yield History shows the whole book (all states)."
                : "Geographic scope of the book numbers below, set by the state filter above."}
              style={{ display: "inline-block", padding: "1px 8px", borderRadius: 10, background: "#eef4ea",
                border: "1px solid #cfe0c8", color: "#3a6e32", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
              📍 {selectedStates.size === 0 ? "All states"
                  : selectedStates.size === 1 ? `${[...selectedStates][0]} only`
                  : `${selectedStates.size} states`}
            </span>
            <span style={{ fontSize: 11, color: "#60735f", fontWeight: 400 }}>click row to see county breakdown</span>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f4f8f4" }}>
                <th style={{ textAlign: "left", padding: "10px 6px 10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0", width: 20 }}></th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Year</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Your Book</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#6b9bd2", borderBottom: "1px solid #e0eae0" }}>{stateAvgLabel}</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Reported Acres</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Farmers</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>vs State</th>
              </tr>
            </thead>
            <tbody>
              {[...yieldRows].reverse().map(r => {
                const nassYld = getNassStateYield(r.year);
                const delta = r.yield != null && nassYld != null ? r.yield - nassYld : null;
                const thin = r.farmers < 50 && activeFilterCount === 0;
                const isExp = expandedYear === r.year;
                const countyRows = isExp ? getCountyBreakdown(r.year) : [];
                return (
                  <React.Fragment key={r.year}>
                    <tr onClick={() => { setExpandedYear(isExp ? null : r.year); setExpandedCounty(null); }}
                      style={{ borderBottom: "1px solid #f0f4f0", opacity: thin ? 0.55 : 1, cursor: "pointer", background: isExp ? `${cc}08` : "transparent" }}>
                      <td style={{ padding: "10px 6px 10px 14px", color: "#60735f", fontSize: 12 }}>{isExp ? '\u25bc' : '\u25b6'}</td>
                      <td style={{ padding: "10px 14px", fontWeight: 700, color: "#1a2e1a" }}>
                        {r.year}{thin && <span style={{ fontSize: 10, color: "#c0392b", marginLeft: 4 }} title="Low sample size">{'\u26a0\ufe0f'}</span>}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 15, color: cc }}>{fmtY(r.yield)} <span style={{ fontSize: 10, fontWeight: 400, color: "#aaa" }}>bu/ac</span></td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 13, color: "#6b9bd2" }}>
                        {nassYld != null ? nassYld.toFixed(1) : '\u2014'}
                        {nassYld != null && isNassForecast(r.year) && <span style={{ fontSize: 10, color: "#999", marginLeft: 2 }}>(f)</span>}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#2d4a2d" }}>{fmtAc(r.acres)}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: thin ? "#c0392b" : "#4a6e4a", fontWeight: thin ? 700 : 400 }}>{r.farmers}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700,
                        color: delta == null ? "#ccc" : delta >= 0 ? "#059669" : "#DC2626" }}>
                        {delta != null ? (delta >= 0 ? '+' : '') + delta.toFixed(1) : '\u2014'}
                      </td>
                    </tr>
                    {isExp && countyRows.length > 0 && (<>
                      <tr style={{ background: "#f8faf8" }}>
                        <td></td>
                        <td style={{ padding: "6px 14px", fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.05em" }}>County</td>
                        <td style={{ padding: "6px 14px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase" }}>Your Book</td>
                        <td style={{ padding: "6px 14px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#6b9bd2", textTransform: "uppercase" }}>NASS</td>
                        <td style={{ padding: "6px 14px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase" }}>Acres</td>
                        <td style={{ padding: "6px 14px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase" }}>Farmers</td>
                        <td></td>
                      </tr>
                      {countyRows.map(cr => {
                        const cKey = `${r.year}|${cr.state}|${cr.county}`;
                        const isCExp = expandedCounty === cKey;
                        const farmerRows = isCExp ? getFarmerBreakdown(r.year, cr.state, cr.county) : [];
                        const nassCty = getNassCountyYield(r.year, cr.state, cr.county);
                        const cDelta = cr.yield != null && nassCty != null ? cr.yield - nassCty : null;
                        return (<React.Fragment key={cKey}>
                          <tr onClick={() => setExpandedCounty(isCExp ? null : cKey)}
                            style={{ background: isCExp ? "#f0f5f0" : "#fafcfa", cursor: "pointer", borderBottom: "1px solid #f0f4f0" }}>
                            <td></td>
                            <td style={{ padding: "8px 14px", fontSize: 12, color: "#1a2e1a", fontWeight: 600 }}>
                              <span style={{ color: "#677167", fontSize: 10, marginRight: 6 }}>{isCExp ? '\u25bc' : '\u25b6'}</span>{titleCase(cr.county)}
                              <span style={{ color: "#677167", fontSize: 10, marginLeft: 4 }}>({cr.state})</span>
                            </td>
                            <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 700, color: cc }}>
                              {fmtY(cr.yield)}
                            </td>
                            <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12, color: "#6b9bd2" }}>
                              {nassCty != null ? nassCty.toFixed(1) : '\u2014'}
                              {cDelta != null && <span style={{ fontSize: 10, marginLeft: 3, color: cDelta >= 0 ? "#059669" : "#DC2626" }}>({cDelta >= 0 ? '+' : ''}{cDelta.toFixed(0)})</span>}
                            </td>
                            <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12, color: "#4a6e4a" }}>{fmtAc(cr.acres)}</td>
                            <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12, color: "#4a6e4a" }}>{cr.farmers}</td>
                            <td></td>
                          </tr>
                          {isCExp && farmerRows.map((fr, fi) => (
                            <tr key={fi} style={{ background: "#f4f8f4", borderBottom: "1px solid #eef2ee" }}>
                              <td></td>
                              <td style={{ padding: "6px 14px 6px 40px", fontSize: 11, color: "#4a6e4a" }}>{fr.name}</td>
                              <td style={{ padding: "6px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12, fontWeight: 600, color: "#2d4a2d" }}>{fmtY(fr.yield)}</td>
                              <td></td>{/* no per-farmer NASS — column reserved for state avg */}
                              <td style={{ padding: "6px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 11, color: "#60735f" }}>{fmtAc(fr.acres)}</td>
                              <td colSpan={2}></td>
                            </tr>
                          ))}
                        </React.Fragment>);
                      })}
                      <tr><td colSpan={7} style={{ height: 8, background: "#fff" }}></td></tr>
                    </>)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── SCO/ECO Trigger Estimate ──────────────────────────────────────── */}
      {(() => {
        const scoEcoYear = 2025;
        const scoEcoCrop = RMA_COUNTY_EXPECTED_REVENUE[scoEcoYear]?.[selectedCrop];
        if (!scoEcoCrop) return null;

        // Aggregate book production for 2025 × selectedCrop, keyed STATE|COUNTY
        // so multi-state data (NY + PA, with same-name counties like Erie /
        // Tioga / Wyoming) doesn't collide.
        const bookByKey = {};
        prodData
          .filter(r => r.crop_year === scoEcoYear
                    && r.crop === selectedCrop
                    && !r.excluded_from_avg
                    && isValidProdRow(r))
          .forEach(r => {
            const st = (r.state || '').toUpperCase().trim();
            const cty = (r.county || '').toUpperCase().trim();
            if (!st || !cty) return;
            const key = `${st}|${cty}`;
            if (!bookByKey[key]) bookByKey[key] = { prod: 0, acres: 0, farmers: new Set() };
            bookByKey[key].prod += Number(r.production || 0);
            bookByKey[key].acres += Number(r.acres || 0);
            if (r.farmer_name_raw) bookByKey[key].farmers.add(r.farmer_name_raw);
          });

        // Walk every (state, county) in the RMA data set. Apply state/county
        // filter chips. Skip rows where neither RMA Final nor a book yield
        // is available — the row would be all "—" and add only noise.
        const rows = [];
        const stateList = Object.keys(scoEcoCrop).sort();
        for (const state of stateList) {
          if (selectedStates.size > 0 && !selectedStates.has(state)) continue;
          const byCounty = scoEcoCrop[state];
          const counties = Object.keys(byCounty).sort();
          for (const county of counties) {
            if (selectedCounties.size > 0 && !selectedCounties.has(county)) continue;
            const rma = byCounty[county];
            const b = bookByKey[`${state}|${county}`];
            const bookYield = b && b.acres > 0 ? b.prod / b.acres : null;
            const bookAcres = b?.acres || 0;
            const bookFarmers = b?.farmers?.size || 0;
            const nassYield = getNassCountyYield(scoEcoYear, state, county);
            // Yield source priority: RMA Final (already published — wheat 2025) →
            // NASS Official (when published — corn/soy after ~June) → book proxy.
            let source, yieldToUse;
            if (rma.finalCountyYield != null) {
              source = 'rma_final';
              yieldToUse = rma.finalCountyYield;
            } else if (nassYield != null) {
              source = 'nass';
              yieldToUse = nassYield;
            } else if (bookYield != null) {
              source = 'book';
              yieldToUse = bookYield;
            } else {
              source = 'none';
              yieldToUse = null;
            }
            // Low-confidence flag fires ONLY when the trigger calc is actually
            // using our book yield (small book = uncertain estimate). When RMA
            // Final or NASS Official drives the calc, the result is canonical
            // regardless of how big our exposure is in that county.
            const lowSample = source === 'book' && (bookAcres < 500 || bookFarmers < 2);
            // Skip counties with no actual yield available — they'd show all "—"
            // and just inflate the table. With 100+ NY+PA counties in the data,
            // this keeps the matrix focused on counties we can actually score.
            if (yieldToUse == null) continue;
            const isOfficial = source === 'rma_final' || source === 'nass';
            // Actual county revenue = (yield) × harvest price. For revenue plans
            // RMA uses harvest price for Final Revenue, not max(projected,harvest).
            const actualRevenue = yieldToUse * rma.harvestPrice;
            const { ratio, payouts } = computeScoEcoTrigger({ actualRevenue, expectedRevenue: rma.expectedRevenue, scoUnderlying, cropYear: scoEcoYear });
            rows.push({ state, county, rma, bookYield, bookAcres, bookFarmers, lowSample, nassYield, source, isOfficial, yieldToUse, actualRevenue, ratio, payouts });
          }
        }

        if (rows.length === 0) return null;

        const allRmaFinal = rows.every(r => r.source === 'rma_final');
        const allOfficial = rows.every(r => r.isOfficial);
        const someOfficial = rows.some(r => r.isOfficial);
        const statusPill = allRmaFinal
          ? { bg: '#d6efd2', fg: '#1a5a1a', label: '🟢 Official · RMA Final county yields' }
          : allOfficial
            ? { bg: '#d6efd2', fg: '#1a5a1a', label: '🟢 Official · NASS county yields' }
            : someOfficial
              ? { bg: '#fdf2c6', fg: '#7a5a1a', label: '🟡 Mixed · some official, some book estimate' }
              : { bg: '#fdf2c6', fg: '#7a5a1a', label: '🟡 Estimate · using book yields, awaiting NASS' };

        const fmtPct = (p) => {
          if (p == null) return <span style={{ color: '#aaa' }}>—</span>;
          if (p < 0.5) return <span style={{ color: '#aaa' }}>—</span>;
          const rounded = Math.round(p);
          const isFull = rounded >= 100;
          return (
            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: isFull ? 800 : 700, color: isFull ? '#1a5a1a' : '#2d4a2d' }}>
              {rounded}%
            </span>
          );
        };

        const fmtMoney = (n) => n == null ? '—' : '$' + n.toFixed(2);
        const eco95Bg = '#eaf3e7';     // headline column tint
        const eco90Bg = '#f4f6f4';     // muted secondary column tint

        // CSV export: mirror what the user sees in the table. Includes a
        // metadata header (crop, year, MPCI underlying, filter, generated
        // timestamp) so downloaded files are self-describing.
        const exportScoEcoCSV = () => {
          const sourceLabel = (s) => ({
            rma_final: 'RMA Final',
            nass:      'NASS Official',
            book:      'Book Estimate',
            none:      'No data',
          }[s] || s);
          const pct = (p) => p == null ? '' : Math.round(p);
          const num = (n, d = 2) => n == null ? '' : Number(n).toFixed(d);
          const esc = (v) => {
            const s = String(v ?? '');
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          };
          const filterDescCSV = (selectedStates.size > 0 || selectedCounties.size > 0)
            ? ([...selectedStates].join('+') + (selectedCounties.size > 0 ? ' / ' + [...selectedCounties].join('+') : ''))
            : 'All states + counties';
          const meta = [
            `SCO/ECO Trigger Estimate — Clover Ag`,
            `Crop: ${selectedCrop}`,
            `Crop Year: ${scoEcoYear}`,
            `SCO Underlying MPCI: ${scoUnderlying}%`,
            `Filter: ${filterDescCSV}`,
            `Generated: ${new Date().toISOString().slice(0, 19).replace('T', ' ')} UTC`,
            ``,
          ].join('\n');
          const headers = [
            'State', 'County', 'Yield Source', 'Expected County Yield (bu/ac)', 'Yield (bu/ac)', 'RMA Final Yield (bu/ac)', 'Book Acres', 'Book Farmers',
            'Low Sample', 'Actual Revenue ($/ac)', 'Expected Revenue ($/ac)', 'Revenue Ratio (%)',
            `SCO ${scoUnderlying}-86% Payout (%)`, 'ECO 95% Payout (%)', 'ECO 90% Payout (%)',
          ];
          const lines = [headers.map(esc).join(',')];
          for (const r of rows) {
            const ratioPct = r.ratio != null ? (r.ratio * 100).toFixed(1) : '';
            lines.push([
              r.state,
              titleCase(r.county),
              sourceLabel(r.source),
              num(r.rma.expectedYield, 1),
              num(r.yieldToUse, 1),
              num(r.rma.finalCountyYield, 1),
              r.bookAcres ? Math.round(r.bookAcres) : '',
              r.bookFarmers || '',
              r.lowSample ? 'Y' : '',
              num(r.actualRevenue),
              num(r.rma.expectedRevenue),
              ratioPct,
              pct(r.payouts.sco),
              pct(r.payouts.eco95),
              pct(r.payouts.eco90),
            ].map(esc).join(','));
          }
          const csv = meta + lines.join('\n') + '\n';
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `sco_eco_${scoEcoYear}_${selectedCrop.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        };

        return (
          <div style={{ marginTop: 16, background: '#fff', border: '1px solid #d0e0d0', borderRadius: 10, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 4 }}>
              <h3 style={{ margin: 0, color: '#1a2e1a', fontSize: 16, fontWeight: 800 }}>
                SCO / ECO Trigger Estimate · 2025 {titleCase(selectedCrop)}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 11, color: '#4a6e4a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  Underlying MPCI:
                  <select
                    value={scoUnderlying}
                    onChange={e => setScoUnderlying(Number(e.target.value))}
                    style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #d0e0d0', background: '#fff', fontSize: 11, fontWeight: 700, color: '#1a2e1a', cursor: 'pointer' }}
                  >
                    {[50, 55, 60, 65, 70, 75, 80, 85].map(v => (
                      <option key={v} value={v}>{v}%</option>
                    ))}
                  </select>
                </label>
                <button
                  onClick={exportScoEcoCSV}
                  title="Download the visible rows as CSV"
                  style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d0e0d0', background: '#f8faf8', color: '#4a6e4a', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {'📥'} Export CSV
                </button>
                <span style={{ background: statusPill.bg, color: statusPill.fg, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 12 }}>
                  {statusPill.label}
                </span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#60735f', marginBottom: 12 }}>
              Actual revenue = yield × harvest price, compared to RMA's expected county revenue.
              Yield source per row: <strong style={{ color: '#1a5a1a' }}>RMA Final</strong> when RMA has published, else <strong style={{ color: '#1a5a1a' }}>NASS Official</strong> when available, else <strong style={{ color: '#7a5a1a' }}>Book Estimate</strong>.
              Percentages = % of max ECO/SCO liability that pays out (multiply by each farmer's own liability for $ indemnity).
              {' '}<strong style={{ color: '#1a5a1a' }}>ECO 95%</strong> is the book default; ECO 90% shown for reference.
              {' '}⚠ = small book sample.
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f4f8f4', borderBottom: '2px solid #d0e0d0' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#4a6e4a' }}>State</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#4a6e4a' }}>County</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#4a6e4a' }}>Yield (bu/ac)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#4a6e4a' }} title="RMA Expected (trend-adjusted) county yield set at planting — the benchmark behind Expected Rev.">Expected Yld</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#1a5a1a', fontWeight: 800 }} title="Official RMA Final Area/County Yield (PM-26-029, released 6/11/2026). Blank = not published for this county.">RMA Final</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#4a6e4a' }}>Source</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#4a6e4a' }}>Actual Rev</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#4a6e4a' }}>Expected Rev</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#4a6e4a' }}>Rev Ratio</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#4a6e4a' }}>SCO ({scoUnderlying}-86%)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#1a5a1a', background: eco95Bg, fontWeight: 800 }}>ECO 95% ★</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#60735f', background: eco90Bg }}>ECO 90%</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const rowOpacity = r.lowSample ? 0.55 : 1;
                    const ratioPct = r.ratio != null ? (r.ratio * 100) : null;
                    const ratioColor = ratioPct == null ? '#aaa' : ratioPct < 86 ? '#a32020' : ratioPct < 95 ? '#7a5a1a' : '#1a5a1a';
                    return (
                      <tr key={`${r.state}|${r.county}`} style={{ borderBottom: '1px solid #eef2ee', opacity: rowOpacity }}>
                        <td style={{ padding: '8px 12px', fontSize: 11, color: '#4a6e4a', fontWeight: 700 }}>{r.state}</td>
                        <td style={{ padding: '8px 12px', color: '#1a2e1a', fontWeight: 600 }}>
                          {titleCase(r.county)}
                          {r.lowSample && <span title={`Low confidence — only ${r.bookFarmers} farm${r.bookFarmers === 1 ? '' : 's'} / ${Math.round(r.bookAcres)} ac in our book`} style={{ marginLeft: 6, color: '#a32020', fontSize: 11 }}>⚠</span>}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>
                          {r.yieldToUse != null ? r.yieldToUse.toFixed(1) : <span style={{ color: '#aaa' }}>—</span>}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: "tabular-nums", color: '#60735f' }}>
                          {r.rma.expectedYield != null ? r.rma.expectedYield.toFixed(1) : <span style={{ color: '#aaa' }}>—</span>}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: r.rma.finalCountyYield != null ? '#1a5a1a' : '#ccc' }}>
                          {r.rma.finalCountyYield != null ? r.rma.finalCountyYield.toFixed(1) : '—'}
                        </td>
                        <td style={{ padding: '8px 12px', fontSize: 11, color: r.isOfficial ? '#1a5a1a' : (r.yieldToUse != null ? '#7a5a1a' : '#aaa') }}>
                          {r.source === 'rma_final'
                            ? 'RMA Final'
                            : r.source === 'nass'
                              ? 'NASS Official'
                              : r.source === 'book'
                                ? `Book (${r.bookFarmers}f / ${Math.round(r.bookAcres).toLocaleString()} ac)`
                                : 'No book data'}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{fmtMoney(r.actualRevenue)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: "tabular-nums", color: '#60735f' }}>{fmtMoney(r.rma.expectedRevenue)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: ratioColor }}>
                          {ratioPct != null ? ratioPct.toFixed(1) + '%' : '—'}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtPct(r.payouts.sco)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', background: eco95Bg }}>{fmtPct(r.payouts.eco95)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', background: eco90Bg }}>{fmtPct(r.payouts.eco90)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11, color: '#60735f', marginTop: 10, lineHeight: 1.5 }}>
              <strong style={{ color: '#4a6e4a' }}>Read this as:</strong> <em>"Livingston ECO 95% pays 100% of max liability"</em> means every Livingston {selectedCrop.toLowerCase()} ECO 95% policy receives its full ECO liability when official county yields are released.
              {' '}Cells flip from Book Estimate → NASS Official automatically when 2025 county yields land in our nass_data table.
              {' '}SCO range = {scoUnderlying}-86% (set by the underlying MPCI selector above); ECO bands are independent of underlying.
            </div>
          </div>
        );
      })()}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// ACRES PLANTED TAB — NY county-level NASS planted acres
// Crops: CORN, SOYBEANS only. WHEAT county-level planted acres
// stops in 2016 (NASS publication gap), so it's excluded.
// Aggregates summed county acres per year; selecting counties
// filters the aggregation. The "OTHER (COMBINED) COUNTIES" NASS
// rollup is included in totals but hidden from the filter chips.
// ═══════════════════════════════════════════════════════════════
function AcresPlantedTab({ isMobile }) {
  const [nassData, setNassData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCrop, setSelectedCrop] = useState('CORN');
  const [selectedCounties, setSelectedCounties] = useState(new Set());
  const [showCountyFilter, setShowCountyFilter] = useState(false);
  const [expandedYear, setExpandedYear] = useState(null);

  const CROPS = ['CORN', 'SOYBEANS'];
  const CROP_COLORS = { CORN: '#D97706', SOYBEANS: '#059669' };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await sbFetch("nass_data", {
          select: "state,county,crop,year,value,fetched_at",
          filters: ["stat_type=eq.ACRES_PLANTED", "state=eq.NY", "crop=in.(CORN,SOYBEANS)", "year=gte.2015"]
        }).catch(() => []);
        // NASS occasionally returns rows with null county (state-aggregate); only keep county-level rows
        setNassData((data || []).filter(r => r.county));
      } catch (e) { console.error("Acres tab load error:", e); }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}>Loading planted acres data...</div>;

  const toggleCounty = (co) => {
    const next = new Set(selectedCounties);
    if (next.has(co)) next.delete(co); else next.add(co);
    setSelectedCounties(next);
  };
  const clearFilters = () => setSelectedCounties(new Set());

  const titleCase = (s) => (s||'').charAt(0).toUpperCase() + (s||'').slice(1).toLowerCase();
  const cc = CROP_COLORS[selectedCrop];
  const fmtAc = (v) => v != null ? Math.round(v).toLocaleString() : '—';
  const fmtAcCompact = (v) => {
    if (v == null) return '—';
    if (v >= 1e6) return (v/1e6).toFixed(2) + 'M';
    if (v >= 1000) return Math.round(v/1000) + 'K';
    return Math.round(v).toLocaleString();
  };

  // Hide NASS's "OTHER (COMBINED) COUNTIES" rollup from the filter chips —
  // it's a suppression aggregate, not a real county. We still include its
  // acres in the unfiltered total so the chart matches NASS state totals.
  const availableCounties = [...new Set(
    nassData
      .filter(r => r.crop === selectedCrop)
      .map(r => (r.county||'').toUpperCase().trim())
      .filter(c => c && !c.includes('OTHER'))
  )].sort();

  // Apply crop + county filter. When no counties are selected, include every
  // county-level row (including the OTHER rollup) so the totals match NASS.
  const filteredRows = nassData.filter(r => {
    if (r.crop !== selectedCrop) return false;
    if (selectedCounties.size > 0 && !selectedCounties.has((r.county||'').toUpperCase().trim())) return false;
    return true;
  });

  // Aggregate acres by year. Track the NASS "OTHER COUNTIES" rollup separately
  // from named counties so the per-year "Counties" count reflects only named
  // counties and the drill-down can label the rollup distinctly.
  const isOtherRollup = (c) => (c||'').toUpperCase().includes('OTHER');
  const acresByYear = {};
  filteredRows.forEach(r => {
    const yr = r.year;
    if (!acresByYear[yr]) acresByYear[yr] = { acres: 0, otherAcres: 0, counties: new Set() };
    acresByYear[yr].acres += Number(r.value || 0);
    if (r.county && isOtherRollup(r.county)) {
      acresByYear[yr].otherAcres += Number(r.value || 0);
    } else if (r.county) {
      acresByYear[yr].counties.add((r.county||'').toUpperCase().trim());
    }
  });
  const acresYears = Object.keys(acresByYear).map(Number).sort((a, b) => a - b);
  const acresRows = acresYears.map(yr => ({
    year: yr,
    acres: acresByYear[yr].acres,
    otherAcres: acresByYear[yr].otherAcres,
    counties: acresByYear[yr].counties.size,
  }));

  const allTimeAvg = acresRows.length > 0
    ? acresRows.reduce((s, r) => s + (r.acres || 0), 0) / acresRows.length
    : null;

  const activeFilterCount = selectedCounties.size;

  // County breakdown for an expanded year. Named counties first (sorted by
  // acres desc), then NASS's "OTHER COUNTIES" privacy-suppression rollup at
  // the bottom flagged as `isOther` so the UI can label it distinctly.
  const getCountyBreakdown = (yr) => {
    const all = filteredRows
      .filter(r => r.year === yr)
      .map(r => ({
        county: (r.county||'').toUpperCase().trim(),
        acres: Number(r.value||0),
        isOther: isOtherRollup(r.county),
      }));
    const named = all.filter(c => !c.isOther).sort((a, b) => b.acres - a.acres);
    const other = all.filter(c => c.isOther);
    return [...named, ...other];
  };

  // Latest NASS year across the loaded dataset for the active crop — drives the freshness hint.
  // Computed off raw nassData (not filteredRows) so the hint represents dataset freshness, not view scope.
  const latestNassYear = (() => {
    const years = nassData.filter(r => r.crop === selectedCrop).map(r => r.year).filter(Boolean);
    return years.length > 0 ? Math.max(...years) : null;
  })();

  // CSV export — summary + per-county detail
  const exportCSV = () => {
    const headers = ['Year','Total Acres','Counties Reporting'];
    const summaryRows = [...acresRows].reverse().map(r => [r.year, Math.round(r.acres), r.counties]);
    const detailHeaders = ['Year','County','Acres Planted'];
    const detailRows = [];
    [...acresYears].reverse().forEach(yr => {
      getCountyBreakdown(yr).forEach(c => detailRows.push([yr, c.county, Math.round(c.acres)]));
    });
    const filterDesc = activeFilterCount > 0 ? [...selectedCounties].join('+') : 'All NY counties';
    const esc = v => String(v).includes(',') ? `"${v}"` : v;
    let csv = `${selectedCrop} Planted Acres - NY (NASS Quick Stats)\nFilters: ${filterDesc}\n\n`;
    csv += 'SUMMARY\n' + headers.join(',') + '\n' + summaryRows.map(r => r.join(',')).join('\n');
    csv += '\n\nDETAIL BY COUNTY\n' + detailHeaders.join(',') + '\n' + detailRows.map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `${selectedCrop.toLowerCase()}_planted_acres_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Linear regression trendline (slope = acres per year)
  const trendLine = (() => {
    const pts = acresRows.filter(r => r.acres > 0);
    if (pts.length < 2) return null;
    const n = pts.length;
    const sumX = pts.reduce((s, r) => s + r.year, 0), sumY = pts.reduce((s, r) => s + r.acres, 0);
    const sumXY = pts.reduce((s, r) => s + r.year * r.acres, 0), sumX2 = pts.reduce((s, r) => s + r.year * r.year, 0);
    const denom = n * sumX2 - sumX * sumX;
    if (denom === 0) return null;
    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    return { slope, intercept, predict: (yr) => slope * yr + intercept };
  })();
  const trendPerYear = trendLine ? Math.round(trendLine.slope) : null;

  // SVG chart geometry — wider left padding than YieldsTab to fit comma-separated acre labels
  const chartW = 720, chartH = 300, padL = 70, padR = 30, padT = 30, padB = 40;
  const plotW = chartW - padL - padR, plotH = chartH - padT - padB;
  const acresVals = acresRows.map(r => r.acres).filter(v => v > 0);
  const trendVals = trendLine && acresYears.length > 0
    ? [trendLine.predict(acresYears[0]), trendLine.predict(acresYears[acresYears.length-1])]
    : [];
  const allYVals = [...acresVals, ...trendVals].filter(v => v > 0);
  // Axis tick step scales with magnitude — single-county series live around 20K,
  // statewide totals around 1M. Pick a step that yields ~5–8 ticks.
  const yStep = (() => {
    const max = allYVals.length ? Math.max(...allYVals) : 100000;
    if (max > 1.5e6) return 500000;
    if (max > 700000) return 200000;
    if (max > 200000) return 100000;
    if (max > 80000) return 20000;
    if (max > 20000) return 5000;
    if (max > 5000) return 1000;
    return 500;
  })();
  const yMin = allYVals.length > 0
    ? Math.max(0, Math.floor(Math.min(...allYVals) / yStep) * yStep - yStep)
    : 0;
  const yMax = allYVals.length > 0
    ? Math.ceil(Math.max(...allYVals) / yStep) * yStep + yStep
    : 100000;
  const minYr = acresYears.length > 0 ? acresYears[0] : 2015;
  const maxYr = acresYears.length > 0 ? acresYears[acresYears.length - 1] : 2024;
  const yearRange = maxYr - minYr || 1;
  const xScale = (yr) => padL + ((yr - minYr) / yearRange) * plotW;
  const yScale = (v) => padT + plotH - ((v - yMin) / (yMax - yMin)) * plotH;
  const yTicks = [];
  for (let v = yMin; v <= yMax; v += yStep) yTicks.push(v);
  const linePath = acresRows.filter(r => r.acres > 0)
    .map((r, i) => `${i === 0 ? 'M' : 'L'}${xScale(r.year).toFixed(1)},${yScale(r.acres).toFixed(1)}`)
    .join(' ');
  const xAxisYears = []; for (let yr = minYr; yr <= maxYr; yr++) xAxisYears.push(yr);

  const filterDesc = activeFilterCount > 0
    ? [...selectedCounties].map(titleCase).join(', ')
    : 'All NY counties';

  const chip = (label, active, onClick) => (
    <button key={label} onClick={onClick} style={{
      padding: "4px 10px", borderRadius: 20, border: active ? `2px solid ${cc}` : "1px solid #d0e0d0",
      background: active ? cc : "#fff", color: active ? "#fff" : "#4a6e4a",
      fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        {latestNassYear != null && (
          <span style={{ fontSize: 11, color: "#677167", fontWeight: 500 }}>
            NASS latest: {latestNassYear}
          </span>
        )}
        <FreshAsOf date={latestTs(nassData, "fetched_at")} label="NASS acres" prefix="synced" />
        <button onClick={exportCSV} disabled={!acresRows.length} style={{
          padding: "6px 14px", borderRadius: 8, border: "1px solid #d0e0d0",
          background: acresRows.length ? "#f8faf8" : "#f0f0f0", color: "#4a6e4a",
          fontSize: 12, fontWeight: 700, cursor: acresRows.length ? "pointer" : "not-allowed",
          display: "flex", alignItems: "center", gap: 4, opacity: acresRows.length ? 1 : 0.5
        }}>
          {'📥'} Export CSV
        </button>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 12, color: "#60735f" }}>
        NY county-level planted acres from NASS Quick Stats {'·'} 2015{'–'}{maxYr} {'·'} click any year row to drill into counties
        {activeFilterCount > 0 && <span style={{ color: cc, fontWeight: 600 }}> {'·'} {filterDesc}</span>}
      </p>

      {/* Crop selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {CROPS.map(crop => (
          <button key={crop} onClick={() => { setSelectedCrop(crop); setExpandedYear(null); }}
            style={{ flex: 1, padding: isMobile ? "10px 8px" : "10px 16px", borderRadius: 10, border: "none", cursor: "pointer",
              background: selectedCrop === crop ? CROP_COLORS[crop] : "#f0f4f0", whiteSpace: "nowrap", fontFamily: "inherit",
              color: selectedCrop === crop ? "#fff" : "#4a6e4a", fontSize: isMobile ? 13.5 : 14, fontWeight: 800 }}>
            {crop === 'CORN' ? '🌽 Corn' : '🫘 Soybeans'}
          </button>
        ))}
      </div>

      {/* County filter */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e0eae0", marginBottom: 20, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a" }}>{'📍'} Filter by County (NY)</span>
          {activeFilterCount > 0 && <button onClick={clearFilters} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #e0d0d0", background: "#fafafa", color: "#c0392b", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Clear all</button>}
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <button onClick={() => setShowCountyFilter(p => !p)} style={{ background: "none", border: "none", color: "#60735f", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
              {showCountyFilter ? '▲ Hide' : `▼ Show (${availableCounties.length})`}
            </button>
            {selectedCounties.size > 0 && <span style={{ fontSize: 10, color: cc, fontWeight: 700 }}>{selectedCounties.size} selected</span>}
          </div>
          {!showCountyFilter && selectedCounties.size > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[...selectedCounties].sort().map(co => chip(titleCase(co), true, () => toggleCounty(co)))}
            </div>
          )}
          {showCountyFilter && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", maxHeight: 200, overflowY: "auto", padding: "4px 0" }}>
              {availableCounties.map(co => chip(titleCase(co), selectedCounties.has(co), () => toggleCounty(co)))}
            </div>
          )}
        </div>
      </div>

      {/* Summary stats */}
      {acresRows.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 140px", padding: "12px 16px", background: "#fff", border: `1px solid ${cc}44`, borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: cc, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{acresYears.length}-Year Avg</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: cc, fontVariantNumeric: "tabular-nums" }}>{fmtAcCompact(allTimeAvg)}</div>
            <div style={{ fontSize: 10, color: "#60735f" }}>acres</div>
          </div>
          <div style={{ flex: "1 1 100px", padding: "12px 16px", background: "#fff", border: "1px solid #e0eae044", borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Latest ({acresYears[acresYears.length-1]})</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{fmtAcCompact(acresRows[acresRows.length-1]?.acres)}</div>
            <div style={{ fontSize: 10, color: "#60735f" }}>acres</div>
          </div>
          <div style={{ flex: "1 1 100px", padding: "12px 16px", background: "#fff", border: "1px solid #e0eae044", borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Most Acres</div>
            {(() => { const best = acresRows.reduce((b, r) => (r.acres||0) > (b.acres||0) ? r : b, acresRows[0]); return best ? <><div style={{ fontSize: 22, fontWeight: 800, color: "#059669", fontVariantNumeric: "tabular-nums" }}>{fmtAcCompact(best.acres)}</div><div style={{ fontSize: 10, color: "#60735f" }}>{best.year}</div></> : null; })()}
          </div>
          <div style={{ flex: "1 1 100px", padding: "12px 16px", background: "#fff", border: "1px solid #e0eae044", borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#4a6e4a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Fewest Acres</div>
            {(() => { const worst = acresRows.filter(r => r.acres > 0).reduce((w, r) => r.acres < w.acres ? r : w, { acres: Infinity }); return worst.acres !== Infinity ? <><div style={{ fontSize: 22, fontWeight: 800, color: "#DC2626", fontVariantNumeric: "tabular-nums" }}>{fmtAcCompact(worst.acres)}</div><div style={{ fontSize: 10, color: "#60735f" }}>{worst.year}</div></> : null; })()}
          </div>
        </div>
      )}

      {/* Trend chart */}
      {acresRows.length > 1 && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e0eae0", marginBottom: 20, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#1a2e1a" }}>Planted Acres Trend</span>
              {allTimeAvg != null && <span style={{ fontSize: 11, color: "#60735f", fontWeight: 400, marginLeft: 8 }}>avg: {fmtAcCompact(allTimeAvg)} ac</span>}
            </div>
            {trendPerYear != null && (
              <span style={{ fontSize: 11, fontWeight: 600, color: trendPerYear >= 0 ? "#059669" : "#DC2626" }}>
                Trend: {trendPerYear >= 0 ? '+' : ''}{trendPerYear.toLocaleString()} ac per year
              </span>
            )}
          </div>
          <div style={{ overflowX: "auto" }}>
            <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: "100%", maxWidth: 1000, display: "block" }}>
              {yTicks.map(v => (
                <g key={v}>
                  <line x1={padL} x2={chartW-padR} y1={yScale(v)} y2={yScale(v)} stroke="#e8ece8" strokeWidth="1" />
                  <text x={padL-8} y={yScale(v)+4} textAnchor="end" fontSize="10" fill="#677167">{fmtAcCompact(v)}</text>
                </g>
              ))}
              {allTimeAvg != null && (
                <line x1={padL} x2={chartW-padR} y1={yScale(allTimeAvg)} y2={yScale(allTimeAvg)} stroke={cc} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.3" />
              )}
              {trendLine && (
                <line x1={xScale(minYr)} y1={yScale(trendLine.predict(minYr))} x2={xScale(maxYr)} y2={yScale(trendLine.predict(maxYr))} stroke="#4a6e4a" strokeWidth="2" strokeDasharray="8,4" opacity="0.6" />
              )}
              <path d={linePath} fill="none" stroke={cc} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {acresRows.filter(r => r.acres > 0).map(r => {
                const isExp = expandedYear === r.year;
                return (
                  <g key={r.year} style={{ cursor: "pointer" }} onClick={() => setExpandedYear(isExp ? null : r.year)}>
                    <circle cx={xScale(r.year)} cy={yScale(r.acres)} r={5} fill={isExp ? "#fff" : cc} stroke={cc} strokeWidth={isExp ? 3 : 2} />
                    <text x={xScale(r.year)} y={yScale(r.acres)-12} textAnchor="middle" fontSize="10" fontWeight="700" fill={cc}>{fmtAcCompact(r.acres)}</text>
                  </g>
                );
              })}
              {xAxisYears.map(yr => (
                <text key={yr} x={xScale(yr)} y={chartH-8} textAnchor="middle" fontSize="10" fill="#60735f" fontWeight="600">{String(yr).slice(2)}</text>
              ))}
              <text x={padL-8} y={padT-10} textAnchor="end" fontSize="9" fill="#aaa">acres</text>
            </svg>
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 10, color: "#60735f", flexWrap: "wrap" }}>
            <span><span style={{ display: "inline-block", width: 16, height: 2, background: cc, verticalAlign: "middle", marginRight: 4 }}></span> Planted acres ({activeFilterCount > 0 ? `${activeFilterCount} ${activeFilterCount === 1 ? 'county' : 'counties'}` : 'NY total'})</span>
            <span><span style={{ display: "inline-block", width: 16, height: 2, background: cc, opacity: 0.3, verticalAlign: "middle", marginRight: 4, borderTop: "1px dashed " + cc }}></span> {acresYears.length}-yr avg</span>
            <span><span style={{ display: "inline-block", width: 16, height: 2, background: "#4a6e4a", opacity: 0.6, verticalAlign: "middle", marginRight: 4, borderTop: "1px dashed #4a6e4a" }}></span> Trendline</span>
          </div>
        </div>
      )}

      {/* Acres by year table with drill-down */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e0eae0", overflow: "hidden" }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #e0eae0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#1a2e1a" }}>Planted Acres by Year</span>
            <span style={{ fontSize: 11, color: "#60735f", fontWeight: 400, marginLeft: 8 }}>click row to see county breakdown</span>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f4f8f4" }}>
                <th style={{ textAlign: "left", padding: "10px 6px 10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0", width: 20 }}></th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Year</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Planted Acres</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Counties</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>YoY</th>
              </tr>
            </thead>
            <tbody>
              {[...acresRows].reverse().map((r, i, arr) => {
                const prior = arr[i+1];
                const yoy = prior && prior.acres > 0 ? (r.acres - prior.acres) / prior.acres : null;
                const isExp = expandedYear === r.year;
                const countyRows = isExp ? getCountyBreakdown(r.year) : [];
                return (
                  <React.Fragment key={r.year}>
                    <tr onClick={() => setExpandedYear(isExp ? null : r.year)}
                      style={{ borderBottom: "1px solid #f0f4f0", cursor: "pointer", background: isExp ? `${cc}08` : "transparent" }}>
                      <td style={{ padding: "10px 6px 10px 14px", color: "#60735f", fontSize: 12 }}>{isExp ? '▼' : '▶'}</td>
                      <td style={{ padding: "10px 14px", fontWeight: 700, color: "#1a2e1a" }}>{r.year}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 15, color: cc }}>
                        {fmtAc(r.acres)} <span style={{ fontSize: 10, fontWeight: 400, color: "#aaa" }}>ac</span>
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#4a6e4a" }}>{r.counties}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700,
                        color: yoy == null ? "#ccc" : yoy >= 0 ? "#059669" : "#DC2626" }}>
                        {yoy != null ? (yoy >= 0 ? '+' : '') + (yoy * 100).toFixed(1) + '%' : '—'}
                      </td>
                    </tr>
                    {isExp && countyRows.length > 0 && (
                      <>
                        <tr style={{ background: "#f8faf8" }}>
                          <td></td>
                          <td style={{ padding: "6px 14px", fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.05em" }}>County</td>
                          <td style={{ padding: "6px 14px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase" }}>Acres</td>
                          <td style={{ padding: "6px 14px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#60735f", textTransform: "uppercase" }}>% of Year</td>
                          <td></td>
                        </tr>
                        {countyRows.map(cr => (
                          <tr key={cr.county} style={{ background: cr.isOther ? "#fdf6e8" : "#fafcfa", borderBottom: "1px solid #f0f4f0" }}>
                            <td></td>
                            <td style={{ padding: "8px 14px", fontSize: 12, color: cr.isOther ? "#7a6530" : "#1a2e1a", fontWeight: 600, fontStyle: cr.isOther ? "italic" : "normal" }}>
                              {cr.isOther ? "Other counties (NASS suppressed)" : titleCase(cr.county)}
                              {cr.isOther && (
                                <span title="NASS combines counties below their disclosure threshold into one 'OTHER COUNTIES' bucket to protect individual operations. Included in the year total but not shown individually."
                                  style={{ marginLeft: 6, fontSize: 10, color: "#c8860a", cursor: "help", fontWeight: 700 }}>ⓘ</span>
                              )}
                            </td>
                            <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 700, color: cr.isOther ? "#9a7a40" : cc }}>{fmtAc(cr.acres)}</td>
                            <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 12, color: "#4a6e4a" }}>
                              {r.acres > 0 ? ((cr.acres/r.acres)*100).toFixed(1) + '%' : '—'}
                            </td>
                            <td></td>
                          </tr>
                        ))}
                        <tr><td colSpan={5} style={{ height: 8, background: "#fff" }}></td></tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// PRICES RECEIVED TAB — NASS state-level prices received
// Grains: annual $/BU. Milk: monthly + annual $/CWT.
// ═══════════════════════════════════════════════════════════════
function PricesReceivedTab({ isMobile }) {
  const [priceData, setPriceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCrop, setSelectedCrop] = useState('CORN');
  const [selectedState, setSelectedState] = useState('NY');

  const CROPS = ['CORN', 'SOYBEANS', 'WHEAT', 'MILK'];
  const STATES = ['NY', 'PA', 'OH', 'IN', 'MD', 'MI'];
  const CROP_COLORS = { CORN: '#D97706', SOYBEANS: '#059669', WHEAT: '#B45309', MILK: '#2563eb' };
  const CROP_LABELS = { CORN: '\ud83c\udf3d Corn', SOYBEANS: '\ud83e\uded8 Soybeans', WHEAT: '\ud83c\udf3e Wheat', MILK: '\ud83e\udd5b Milk' };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await sbFetch("nass_data", {
          select: "state,crop,year,week,value,unit,stat_type,fetched_at",
          filters: ["stat_type=eq.PRICE_RECEIVED"]
        }).catch(() => []);
        setPriceData(data || []);
      } catch (e) { console.error("Prices tab load error:", e); }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}>Loading price data...</div>;

  const cc = CROP_COLORS[selectedCrop] || '#4a6e4a';
  const isMilk = selectedCrop === 'MILK';
  const unitLabel = isMilk ? '$/CWT' : '$/BU';

  const rows = priceData.filter(r => r.crop === selectedCrop && r.state === selectedState);
  const annualRows = rows.filter(r => r.week == null).sort((a, b) => a.year - b.year);
  const monthlyRows = rows.filter(r => r.week != null).sort((a, b) => (a.year * 100 + a.week) - (b.year * 100 + b.week));

  // Labeling:
  //  - Grains (annual): Marketing Year Average. NASS labels year = crop/harvest year.
  //    So NASS year=2024 means the 2024 crop, marketed Sep 2024 - Aug 2025. Display as "2024/25 MYA".
  //  - Milk (monthly): calendar month. Display as "Apr 2025".
  //  - Milk (annual): calendar year. Display as "2024 CY".
  const MONTH_ABBR = ['', 'Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const chartData = isMilk
    ? monthlyRows.map(r => ({
        x: `${r.year}-${String(r.week).padStart(2, '0')}`,
        y: r.value,
        year: r.year,
        label: `${MONTH_ABBR[r.week] || 'M'+r.week} ${r.year}`
      }))
    : annualRows.map(r => ({
        x: `${String(r.year).slice(2)}/${String((r.year + 1) % 100).padStart(2, '0')}`,
        y: r.value,
        year: r.year,
        label: `${r.year}/${String((r.year + 1) % 100).padStart(2, '0')} MYA`
      }));

  const values = chartData.map(d => d.y).filter(v => !isNaN(v));
  const avg = values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;
  const latestVal = chartData.length ? chartData[chartData.length - 1] : null;
  const minVal = values.length ? Math.min(...values) : null;
  const maxVal = values.length ? Math.max(...values) : null;

  const chartW = isMobile ? 340 : 780;
  const chartH = 280;
  const pad = { t: 20, r: 20, b: 40, l: 50 };
  const plotW = chartW - pad.l - pad.r;
  const plotH = chartH - pad.t - pad.b;

  const yMin = minVal != null ? Math.floor(minVal * 0.9 * 10) / 10 : 0;
  const yMax = maxVal != null ? Math.ceil(maxVal * 1.05 * 10) / 10 : 10;
  const yRange = yMax - yMin || 1;

  const xScale = (i) => pad.l + (chartData.length > 1 ? (i / (chartData.length - 1)) * plotW : plotW / 2);
  const yScale = (v) => pad.t + plotH - ((v - yMin) / yRange) * plotH;

  const linePath = chartData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.y)}`).join(' ');

  const exportCSV = () => {
    const hdr = isMilk ? ['Year', 'Month', `Price (${unitLabel})`] : ['Year', `Price (${unitLabel})`];
    const lines = [hdr.join(',')];
    if (isMilk) {
      monthlyRows.forEach(r => lines.push([r.year, r.week, r.value].join(',')));
    } else {
      annualRows.forEach(r => lines.push([r.year, r.value].join(',')));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prices-received-${selectedState}-${selectedCrop}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (v) => v != null ? `$${v.toFixed(2)}` : '\u2014';

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        <button onClick={exportCSV} disabled={!chartData.length} style={{
          padding: "6px 14px", borderRadius: 8, border: "1px solid #d0e0d0",
          background: chartData.length ? "#f8faf8" : "#f0f0f0",
          color: "#4a6e4a", fontSize: 12, fontWeight: 700,
          cursor: chartData.length ? "pointer" : "not-allowed",
          display: "flex", alignItems: "center", gap: 4,
          opacity: chartData.length ? 1 : 0.5
        }}>
          {'\ud83d\udce5'} Export CSV
        </button>
      </div>
      <p style={{ margin: "0 0 6px", fontSize: 12, color: "#60735f" }}>
        NASS state-level prices received {'\u00b7'} {isMilk ? 'monthly calendar prices' : 'Marketing Year Average (MYA)'} {'\u00b7'} {unitLabel}
        {latestTs(priceData, "fetched_at") && <> {'\u00b7'} <FreshAsOf date={latestTs(priceData, "fetched_at")} label="NASS prices" prefix="synced" style={{ display: "inline" }} /></>}
      </p>
      {!isMilk && (
        <p style={{ margin: "0 0 16px", fontSize: 11, color: "#677167", fontStyle: "italic" }}>
          Marketing year runs Sep 1 {'\u2013'} Aug 31 for corn/soy, Jun 1 {'\u2013'} May 31 for wheat. "2024/25 MYA" = the 2024 crop, sold through the following year.
        </p>
      )}
      {isMilk && (
        <p style={{ margin: "0 0 16px", fontSize: 11, color: "#677167", fontStyle: "italic" }}>
          NASS All-Milk Price {'—'} average received for all milk sold, blended across all classes at the state's
          average butterfat/protein test, before hauling/marketing deductions. Not Class III/IV (FMMO component prices)
          or mailbox (net after deductions). Calendar month; no marketing year applies.
        </p>
      )}

      {/* Crop selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {CROPS.map(crop => (
          <button key={crop} onClick={() => setSelectedCrop(crop)}
            style={{
              flex: isMobile ? "1 1 45%" : 1, padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer",
              background: selectedCrop === crop ? CROP_COLORS[crop] : "#f0f4f0",
              color: selectedCrop === crop ? "#fff" : "#4a6e4a", fontSize: 14, fontWeight: 800
            }}>
            {CROP_LABELS[crop]}
          </button>
        ))}
      </div>

      {/* State selector */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {STATES.map(st => (
          <button key={st} onClick={() => setSelectedState(st)}
            style={{
              padding: "6px 14px", borderRadius: 8, border: selectedState === st ? `2px solid ${cc}` : "1px solid #d0e0d0",
              background: selectedState === st ? cc : "#fff",
              color: selectedState === st ? "#fff" : "#4a6e4a",
              fontSize: 12, fontWeight: 700, cursor: "pointer"
            }}>
            {st}
          </button>
        ))}
      </div>

      {/* Stats cards */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
        <div style={{ background: "#f8faf8", border: "1px solid #d0e0d0", borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: "#60735f", fontWeight: 600 }}>LATEST</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: cc }}>{fmt(latestVal?.y)}</div>
          <div style={{ fontSize: 10, color: "#60735f" }}>{latestVal?.label || '\u2014'}</div>
        </div>
        <div style={{ background: "#f8faf8", border: "1px solid #d0e0d0", borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: "#60735f", fontWeight: 600 }}>AVG</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1a2e1a" }}>{fmt(avg)}</div>
          <div style={{ fontSize: 10, color: "#60735f" }}>{values.length} periods</div>
        </div>
        <div style={{ background: "#f8faf8", border: "1px solid #d0e0d0", borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: "#60735f", fontWeight: 600 }}>HIGH</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#059669" }}>{fmt(maxVal)}</div>
        </div>
        <div style={{ background: "#f8faf8", border: "1px solid #d0e0d0", borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: "#60735f", fontWeight: 600 }}>LOW</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#dc2626" }}>{fmt(minVal)}</div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#60735f", background: "#f8faf8", borderRadius: 8, border: "1px solid #d0e0d0" }}>
          No price data available for {selectedCrop} in {selectedState}.
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #d0e0d0", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: "100%", height: "auto", maxWidth: chartW }}>
            {/* Y axis gridlines */}
            {[0, 0.25, 0.5, 0.75, 1].map(t => {
              const val = yMin + yRange * (1 - t);
              const y = pad.t + plotH * t;
              return (
                <g key={t}>
                  <line x1={pad.l} y1={y} x2={chartW - pad.r} y2={y} stroke="#e0e8e0" strokeWidth={1} />
                  <text x={pad.l - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#60735f">${val.toFixed(2)}</text>
                </g>
              );
            })}
            {/* Line */}
            <path d={linePath} fill="none" stroke={cc} strokeWidth={2.5} strokeLinejoin="round" />
            {/* Points */}
            {chartData.map((d, i) => (
              <circle key={i} cx={xScale(i)} cy={yScale(d.y)} r={3} fill={cc}>
                <title>{`${d.label}: $${d.y.toFixed(2)}`}</title>
              </circle>
            ))}
            {/* X axis labels — show every Nth so they don't overlap */}
            {chartData.map((d, i) => {
              const every = Math.max(1, Math.ceil(chartData.length / (isMobile ? 6 : 12)));
              if (i % every !== 0 && i !== chartData.length - 1) return null;
              return (
                <text key={i} x={xScale(i)} y={chartH - pad.b + 16} textAnchor="middle" fontSize={10} fill="#60735f">
                  {isMilk ? d.label : d.x}
                </text>
              );
            })}
          </svg>
        </div>
      )}

      {/* Data table */}
      {chartData.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #d0e0d0", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, background: "#f8faf8", zIndex: 1 }}>
                <tr style={{ borderBottom: "2px solid #d0e0d0" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left", color: "#4a6e4a", fontWeight: 700 }}>Period</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", color: "#4a6e4a", fontWeight: 700 }}>Price ({unitLabel})</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", color: "#4a6e4a", fontWeight: 700 }}>vs Avg</th>
                </tr>
              </thead>
              <tbody>
                {[...chartData].reverse().map((d, i) => {
                  const delta = avg != null ? d.y - avg : null;
                  const pctDelta = avg != null ? (delta / avg) * 100 : null;
                  const deltaColor = delta > 0 ? "#059669" : delta < 0 ? "#dc2626" : "#60735f";
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid #f0f4f0" }}>
                      <td style={{ padding: "8px 12px", color: "#1a2e1a" }}>{d.label}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: "#1a2e1a" }}>{fmt(d.y)}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", color: deltaColor, fontWeight: 600 }}>
                        {delta != null ? `${delta >= 0 ? '+' : ''}${delta.toFixed(2)} (${pctDelta >= 0 ? '+' : ''}${pctDelta.toFixed(1)}%)` : '\u2014'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function CollapsibleSection({ title, subtitle, badge, badgeColor, defaultOpen, children, onExport, exportLabel, accent }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const bg     = accent === "wheat"  ? "#2a1e00" : accent === "red" ? "#4a1a1a" : accent === "spring" ? "#1a3a1a" : accent === "drp" ? "#1a2a4a" : accent === "lrp" ? "#3a1f0a" : accent === "prf" ? "#1a2a3a" : "#1a2e1a";
  const acBdr  = accent === "wheat"  ? "#c8a840" : accent === "red" ? "#e05050" : accent === "spring" ? "#4aaa4a" : accent === "drp" ? "#4a80cc" : accent === "lrp" ? "#cc7030" : accent === "prf" ? "#4a8aaa" : "#4a7a4a";
  return (
    <div style={{ marginBottom:10, borderRadius:10, overflow:"hidden", border:`1px solid ${acBdr}33` }}>
      <button onClick={() => setOpen(o => !o)} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", background: bg, border:"none", cursor:"pointer", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
          <span style={{ fontSize:15, fontWeight:800, color:"#fff", textAlign:"left" }}>{title}</span>
          {subtitle && <span style={{ fontSize:11, color:"#ffffff88", fontWeight:500 }}>{subtitle}</span>}
          {badge != null && (
            <span style={{ fontSize:11, fontWeight:800, padding:"2px 9px", borderRadius:20, background: badgeColor||acBdr, color: badgeColor ? "#fff" : bg, letterSpacing:"0.04em" }}>{badge}</span>
          )}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
          {onExport && open && (
            <span onClick={e=>{e.stopPropagation(); onExport();}} style={{ fontSize:11, fontWeight:700, color:acBdr, background:"rgba(255,255,255,0.1)", border:`1px solid ${acBdr}55`, borderRadius:5, padding:"3px 10px", cursor:"pointer" }}>
              ⬇ {exportLabel||"CSV"}
            </span>
          )}
          <span style={{ color: acBdr, fontSize:16, transition:"transform 0.2s", display:"inline-block", transform: open?"rotate(180deg)":"rotate(0deg)" }}>▾</span>
        </div>
      </button>
      {open && (
        <div style={{ background:"#fff", padding:"18px 20px", overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          {children}
        </div>
      )}
    </div>
  );
}


function YieldHistorySection({ farmers }) {
  const isMobile = useIsMobile();
  const [yieldRows, setYieldRows]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [importing, setImporting]           = useState(false);
  const [importMsg, setImportMsg]           = useState(null);
  const [selectedCrop, setSelectedCrop]     = useState('CORN');
  const [expandedCounty, setExpandedCounty] = useState(null);
  const [showImport, setShowImport]         = useState(false);
  const [sortKey, setSortKey]               = useState('county');
  const [sortDir, setSortDir]               = useState('asc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'county' ? 'asc' : 'desc'); }
  };
  const SortArrow = ({ col }) => sortKey !== col ? <span style={{color:'#bbb',marginLeft:3}}>⇅</span>
    : sortDir === 'asc' ? <span style={{color:'#3a7a3a',marginLeft:3}}>↑</span>
    : <span style={{color:'#3a7a3a',marginLeft:3}}>↓</span>;
  const [matchQueue, setMatchQueue]         = useState([]); // rows needing manual match
  const [matchIdx, setMatchIdx]             = useState(0);
  const [matchedFarmer, setMatchedFarmer]   = useState('');
  // The last COMPLETED harvest: the year before the book's (rollover, Kevin
  // 2026-11-02). Was 2025, fixed. Imports are stamped with it and the import panel
  // names it, so a file is never filed under a year nobody saw.
  const CROP_YEAR = CURRENT_CROP_YEAR - 1;
  const DISPLAY_CROPS = ['CORN','SOYBEANS','WHEAT'];

  // Build farmer lookup: policy_number -> farmer_id
  const policyToFarmer = useMemo(() => {
    const map = {};
    (farmers || []).forEach(f => {
      (f.policies || []).forEach(p => {
        if (p.policy_number) map[String(p.policy_number)] = { id: f.id, name: f.name };
      });
    });
    return map;
  }, [farmers]);

  // Resolve a production_history row to a display farmer { id, name }.
  // The M13/NAU sync stores farmer_name_raw = policy.farmerName || policy.policyNumber,
  // so individual-insured policies (blank farmerName at sync time) land here labelled
  // with their policy number. The real name lives on the linked farmer — recover it
  // via policy_number, but only override when the raw label looks like a policy number
  // so genuine entity names captured at sync time are never replaced.
  const looksLikePolicyNumber = (s) => {
    const v = (s || '').trim();
    return !v || /^MP-\d/i.test(v) || /^[A-Z]{2}-\d{3}-\d+-\d+$/i.test(v);
  };
  const resolveFarmer = (r) => {
    const linked = policyToFarmer[r.policy_number];
    const raw = r.farmer_name_raw;
    const id = r.farmer_id || linked?.id || `__raw__${raw}`;
    const name = (raw && !looksLikePolicyNumber(raw)) ? raw : (linked?.name || raw);
    return { id, name };
  };

  // Load yield rows from DB
  const loadYields = async () => {
    setLoading(true);
    try {
      // Load ALL production rows (grain + silage) for progress tracking
      // Yield averages will filter to grain-only (excluded_from_avg=false) client-side
      const rows = await sbFetch('production_history', {
        filters: [`crop_year=eq.${CROP_YEAR}`,
                  `crop=in.(CORN,SOYBEANS,WHEAT)`, 'source_file=eq.M13_auto']
      });
      setYieldRows(rows || []);
    } catch(e) { console.warn('Yield load error:', e); }
    setLoading(false);
  };

  useEffect(() => { loadYields(); }, []);

  // ── Weighted average helpers ──
  const weightedAvg = (rows) => {
    const totalProd = rows.reduce((s,r) => s + (Number(r.production)||0), 0);
    const totalAc   = rows.reduce((s,r) => s + (Number(r.acres)||0), 0);
    return totalAc > 0 ? totalProd / totalAc : null;
  };

  // ── Parse NAU Excel (xlsx) — loads SheetJS from cdnjs (allowed domain), converts to JSON client-side ──
  const parseNAUExcel = async (file) => {
    // Load SheetJS if not present — cdnjs is in the app's allowed domains
    if (!window.XLSX) {
      await new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        s.onload = res; s.onerror = () => rej(new Error('Could not load xlsx parser. Please save as CSV and upload instead.'));
        document.head.appendChild(s);
      });
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = window.XLSX.read(e.target.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          resolve(window.XLSX.utils.sheet_to_json(ws, { defval: '' }));
        } catch(err) { reject(err); }
      };
      reader.onerror = () => reject(new Error('File read failed'));
      reader.readAsArrayBuffer(file);
    });
  };

  // ── Process NAU rows (keys match Claude extraction output) ──
  const processNAURows = (rawRows) => {
    const results = [];
    rawRows.forEach(row => {
      // Support both Claude-extracted keys (lowercase) and raw xlsx keys
      const crop     = String(row['crop']||row['Crop']||'').trim().toUpperCase();
      const measure  = String(row['measure']||row['Measure']||'').trim().toUpperCase();
      const county   = String(row['county']||row['County']||'').trim().toUpperCase();
      const rawState = String(row['state']||row['State']||'NY').trim().toUpperCase();
      const state    = rawState === 'NEW YORK' ? 'NY' : rawState.slice(0,2);
      const prod     = Number(row['net_production']||row['Net Production']||0);
      const acres    = Number(row['net_acres']||row['Net Acres']||0);
      const policyFull = String(row['policy_number']||row['Policy Number']||'');
      const farmerRaw  = String(row['insured']||row['Insured']||'').trim();

      // Extract 7-digit policy number
      const m = policyFull.match(/-(\d+)-\d+$/);
      const policyNum = m ? m[1] : policyFull;

      // Determine exclusion
      let excluded = false, reason = null;
      if (measure === 'TON') { excluded = true; reason = 'SILAGE'; }
      else if (acres === 0)  { excluded = true; reason = 'ZERO_ACRES'; }
      else if (prod === 0)   { excluded = true; reason = 'ZERO_PRODUCTION'; }

      results.push({ crop, county, state, acres, production: prod, unit_of_measure: measure,
        policy_number: policyNum, farmer_name_raw: farmerRaw,
        crop_year: CROP_YEAR, aip: 'NAU', excluded_from_avg: excluded,
        exclusion_reason: reason, source_file: 'NAU_Prior_Year_Net_Production.xlsx' });
    });
    return results;
  };

  // ── Parse R&H PDF via Claude API ──
  const parseRHPDF = async (file) => {
    const base64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result.split(',')[1]);
      r.onerror = () => rej(new Error('Read failed'));
      r.readAsDataURL(file);
    });

    const prompt = `You are extracting data from a Rain & Hail "Total Production Report" PDF.
Each page covers one farmer/policy. Extract ALL rows into a JSON array.
For each data row return an object with these exact keys:
- policy_number: the MP-xxxxxxx number from the page header (digits only after MP-)
- farmer_name: farmer name from the page header
- commodity: crop name (CORN, SOYBEANS, WHEAT, DRY BEANS, etc.)
- county: county name in ALL CAPS
- unit_structure: unit type (BU, OU, EU, etc.)
- acres: numeric acres (null if blank)
- weighted_avg_yield: numeric yield per acre (null if blank)
- unit_of_measure: BU or TON or LBS
- total_production: numeric total production (0 if zero)
- net_production: numeric net production (0 if zero)
Skip subtotal/total rows (e.g. "Corn Total"). Only return individual county rows.
Return ONLY valid JSON array, no markdown, no explanation.`;

    const resp = await adminApiFetch('/api/extract', {
      method: 'POST',
      body: JSON.stringify({
        model: 'claude-sonnet-4-6',
        max_tokens: 4000,
        messages: [{ role: 'user', content: [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 }},
          { type: 'text', text: prompt }
        ]}]
      })
    });
    const data = await resp.json();
    const text = (data.content||[]).map(c=>c.text||'').join('');
    const clean = text.replace(/```json|```/g,'').trim();
    return JSON.parse(clean);
  };

  // ── Process R&H rows ──
  const processRHRows = (rawRows, fileName) => {
    return rawRows.map(row => {
      const crop    = String(row.commodity||'').trim().toUpperCase();
      const county  = String(row.county||'').trim().toUpperCase();
      const acres   = Number(row.acres||0);
      const prod    = Number(row.net_production||0);
      const measure = String(row.unit_of_measure||'BU').trim().toUpperCase();
      const policyNum = String(row.policy_number||'').replace(/^MP-0*/,'').trim();
      const farmerRaw = String(row.farmer_name||'').trim();

      let excluded = false, reason = null;
      if (measure === 'TON')      { excluded = true; reason = 'SILAGE'; }
      else if (acres === 0)       { excluded = true; reason = 'ZERO_ACRES'; }
      else if (row.weighted_avg_yield == null || row.weighted_avg_yield === '') { excluded = true; reason = 'NO_YIELD'; }
      else if (prod === 0)        { excluded = true; reason = 'ZERO_PRODUCTION'; }

      return { crop, county, state: 'NY', acres, production: prod, unit_of_measure: measure,
        policy_number: policyNum, farmer_name_raw: farmerRaw,
        crop_year: CROP_YEAR, aip: 'RH', excluded_from_avg: excluded,
        exclusion_reason: reason, source_file: fileName };
    });
  };

  // ── Match & import rows ──
  const matchAndQueue = (processed) => {
    const needsMatch = [];
    const autoMatched = [];
    processed.forEach(row => {
      const hit = policyToFarmer[row.policy_number];
      if (hit) {
        autoMatched.push({ ...row, farmer_id: hit.id });
      } else {
        needsMatch.push({ ...row, farmer_id: null });
      }
    });
    return { autoMatched, needsMatch };
  };

  const importRows = async (rows) => {
    if (!rows.length) return;
    // Deduplicate by policy+crop+county before insert
    const toInsert = rows.map(r => ({
      farmer_id: r.farmer_id || null,
      farmer_name_raw: r.farmer_name_raw,
      policy_number: r.policy_number,
      crop_year: r.crop_year,
      crop: r.crop,
      county: r.county,
      state: r.state || 'NY',
      acres: r.acres,
      production: r.production,
      unit_of_measure: r.unit_of_measure,
      aip: r.aip,
      excluded_from_avg: r.excluded_from_avg,
      exclusion_reason: r.exclusion_reason,
      source_file: r.source_file,
    }));

    const res = await fetch(`${SUPABASE_URL}/rest/v1/production_history`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Prefer': 'return=minimal' },
      body: JSON.stringify(toInsert),
    });
    if (!res.ok) {
      const err = await res.text();
      throw new Error(err);
    }
  };

  // ── Handle file upload ──
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportMsg(null);
    try {
      let processed = [];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        const raw = await parseNAUExcel(file);
        processed = processNAURows(raw);
      } else if (file.name.endsWith('.pdf')) {
        const raw = await parseRHPDF(file);
        processed = processRHRows(raw, file.name);
      } else {
        setImportMsg({ ok: false, text: 'Please upload an .xlsx (NAU) or .pdf (R&H) file.' });
        setImporting(false); return;
      }

      const { autoMatched, needsMatch } = matchAndQueue(processed);

      // Import auto-matched immediately
      if (autoMatched.length) await importRows(autoMatched);

      if (needsMatch.length > 0) {
        setMatchQueue(needsMatch);
        setMatchIdx(0);
        setMatchedFarmer('');
        setImportMsg({ ok: true, text: `✓ ${autoMatched.length} rows imported. ${needsMatch.length} rows need manual farmer matching below.` });
      } else {
        setImportMsg({ ok: true, text: `✓ ${autoMatched.length} rows imported successfully.` });
        setMatchQueue([]);
        await loadYields();
      }
    } catch(err) {
      console.error('Import error:', err);
      setImportMsg({ ok: false, text: `Import failed: ${err.message}` });
    }
    setImporting(false);
    e.target.value = '';
  };

  // ── Handle manual match for one row ──
  const handleManualMatch = async (skip) => {
    const row = matchQueue[matchIdx];
    const farmerId = skip ? null : (farmers.find(f=>f.name===matchedFarmer)?.id || null);
    if (!skip && !farmerId) { alert('Select a farmer or click Skip'); return; }
    try {
      await importRows([{ ...row, farmer_id: farmerId }]);
    } catch(e) { console.error(e); }
    const next = matchIdx + 1;
    if (next >= matchQueue.length) {
      setMatchQueue([]);
      setMatchIdx(0);
      setImportMsg({ ok: true, text: '✓ All rows processed.' });
      await loadYields();
    } else {
      setMatchIdx(next);
      setMatchedFarmer('');
    }
  };

  // ── Compute display data ──
  // allCropRows: ALL rows for progress tracking (grain + silage — we need to know who has reported)
  // grainRows: grain-only (excluded_from_avg=false) for weighted average yield calculation
  const allCropRows = yieldRows.filter(r => r.crop === selectedCrop && Number(r.acres||0) > 0);
  const grainRows   = allCropRows.filter(r => !r.excluded_from_avg && isValidProdRow(r));
  const bookAvg = weightedAvg(grainRows);

  // Group by county — grain rows only for yield averages
  const byCounty = {};
  grainRows.forEach(r => {
    const c = r.county || 'UNKNOWN';
    if (!byCounty[c]) byCounty[c] = [];
    byCounty[c].push(r);
  });

  // Insured acres for CROP_YEAR from historical_summaries (loaded once)
  const [hist2025Acres, setHist2025Acres] = useState(null);
  useEffect(() => {
    if (hist2025Acres !== null) return; // already loaded
    sbFetch('historical_summaries', {
      filters: [`crop_year=eq.${CROP_YEAR}`]
    }).then(rows => {
      const acc = { CORN:0, SOYBEANS:0, WHEAT:0 };
      const SKIP_PLANS = new Set(['SCO-RP','ECO-RP','SCO','ECO','SCO-YP','ECO-YP','ARPI']);
      (rows||[]).forEach(r => {
        const crop = (r.crop||'').toUpperCase().trim();
        const plan = (r.type||'').toUpperCase().trim();
        const key = (crop==='WINTER WHEAT' || crop==='WHEAT') ? 'WHEAT'
                  : crop==='CORN' ? 'CORN'
                  : crop==='SOYBEANS' ? 'SOYBEANS'
                  : null;
        if (!key || SKIP_PLANS.has(plan)) return;
        acc[key] += Number(r.acres||0);
      });
      setHist2025Acres(acc);
    }).catch(() => setHist2025Acres({ CORN:0, SOYBEANS:0, WHEAT:0 }));
  }, []);

  const bookInsuredAcres = hist2025Acres || { CORN:0, SOYBEANS:0, WHEAT:0 };

  // Build sorted county stats
  const countyStats = Object.keys(byCounty).map(county => {
    const rows       = byCounty[county];
    const avg        = weightedAvg(rows);
    const ac         = rows.reduce((s,r)=>s+Number(r.acres||0),0);
    const farmerCount= [...new Set(rows.map(r=>resolveFarmer(r).id).filter(Boolean))].length;
    return { county, avg, ac, farmerCount };
  });
  countyStats.sort((a,b) => {
    if (sortKey==='county') return sortDir==='asc' ? a.county.localeCompare(b.county) : b.county.localeCompare(a.county);
    if (sortKey==='avg')    return sortDir==='asc' ? (a.avg||0)-(b.avg||0) : (b.avg||0)-(a.avg||0);
    if (sortKey==='ac')     return sortDir==='asc' ? a.ac-b.ac : b.ac-a.ac;
    if (sortKey==='farmerCount') return sortDir==='asc' ? a.farmerCount-b.farmerCount : b.farmerCount-a.farmerCount;
    return 0;
  });
  const counties = countyStats.map(c=>c.county);

  const fmtYield = (y) => y != null ? `${y.toFixed(1)} bu/ac` : '—';
  const fmtAcres = (a) => a != null ? `${Number(a).toLocaleString('en-US',{maximumFractionDigits:0})} ac` : '—';

  const cropColor = { CORN:'#e8a820', SOYBEANS:'#4a7a20', WHEAT:'#c09840' };
  const cropBg    = { CORN:'#fdf5e0', SOYBEANS:'#f0f6e8', WHEAT:'#f8f2e0' };
  const cc = cropColor[selectedCrop]||'#4a6e4a';
  const cb = cropBg[selectedCrop]||'#f4f8f0';

  // Farmers and acres: use grain-only rows (same filter as yield average)
  const totalFarmers = [...new Set(grainRows.map(r => resolveFarmer(r).id).filter(Boolean))].length;
  const totalAcres   = grainRows.reduce((s,r)=>s+Number(r.acres||0),0);
  // Geographic scope of the displayed average. This view is whole-book (all states
  // the agency writes — NY, PA, OH, MI, IN). The Historical Data page intentionally
  // defaults to NY-only for its NASS comparison, so the two headline numbers differ
  // by scope, not by source (both are M13_auto). Surface the scope so that's obvious.
  const bookStates = [...new Set(grainRows.map(r => (r.state||'').toUpperCase().trim()).filter(Boolean))].sort();
  const insuredAcres = bookInsuredAcres[selectedCrop] || 0;
  const pctReported  = insuredAcres > 0 ? Math.round(totalAcres / insuredAcres * 100) : null;

  // ── Export to Excel (CSV download — opens in Excel) ──
  const exportToExcel = () => {
    // Build rows: county summary + farmer detail
    const rows = [];
    // Header
    rows.push([CROP_YEAR + ' Actual Yield History — ' + selectedCrop.charAt(0)+selectedCrop.slice(1).toLowerCase()]);
    rows.push(['Generated', new Date().toLocaleDateString()]);
    rows.push(['Reported Acres', totalAcres.toFixed(0), `Insured Acres (${CROP_YEAR})`, insuredAcres.toFixed(0), '% Reported', pctReported!=null?pctReported+'%':'N/A']);
    rows.push(['Book Wtd Avg Yield', bookAvg!=null?bookAvg.toFixed(1)+' bu/ac':'—']);
    rows.push([]);
    // County + farmer detail
    rows.push(['County','Farmer','Yield (bu/ac)','Acres','Production (bu)','AIP','Policy #']);
    counties.forEach(county => {
      const cRows = byCounty[county];
      const cAvg  = weightedAvg(cRows);
      const cAc   = cRows.reduce((s,r)=>s+Number(r.acres||0),0);
      // County summary row
      rows.push([
        county.charAt(0)+county.slice(1).toLowerCase()+' Co.',
        '— COUNTY TOTAL —',
        cAvg!=null?cAvg.toFixed(1):'—',
        cAc.toFixed(0),
        cRows.reduce((s,r)=>s+Number(r.production||0),0).toFixed(0),
        '',''
      ]);
      // Individual farmer rows
      const farmerYields = {};
      cRows.forEach(r => {
        const { id: fid, name: fname } = resolveFarmer(r);
        if (!farmerYields[fid]) farmerYields[fid] = { name:fname, rows:[] };
        farmerYields[fid].rows.push(r);
      });
      Object.values(farmerYields)
        .sort((a,b)=>(weightedAvg(b.rows)||0)-(weightedAvg(a.rows)||0))
        .forEach(f => {
          const fAvg = weightedAvg(f.rows);
          const fAc  = f.rows.reduce((s,r)=>s+Number(r.acres||0),0);
          const fProd= f.rows.reduce((s,r)=>s+Number(r.production||0),0);
          rows.push([
            '', f.name,
            fAvg!=null?fAvg.toFixed(1):'—',
            fAc.toFixed(1),
            fProd.toFixed(0),
            f.rows[0]?.aip==='RH'?'Rain & Hail':'NAU',
            f.rows[0]?.policy_number||''
          ]);
        });
      rows.push([]); // blank between counties
    });

    // Convert to CSV
    const csv = rows.map(r =>
      r.map(v => {
        const s = String(v==null?'':v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? '"' + s.replace(/"/g,'""') + '"' : s;
      }).join(',')
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `CloverAg_${CROP_YEAR}_Yield_${selectedCrop}_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:16, color:'#1a2e1a' }}>🌽 {CROP_YEAR} Actual Yield History</div>
          <div style={{ fontSize:12, color:'#60735f', marginTop:2, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <span>Weighted average yields by county · source: M13 daily sync (NAU &amp; R&amp;H)</span>
            {bookStates.length > 0 && (
              <span title={`Whole book — all states the agency writes. The Historical Data page shows NY only.`}
                style={{ display:'inline-block', padding:'1px 8px', borderRadius:10, background:'#eef4ea',
                  border:'1px solid #cfe0c8', color:'#3a6e32', fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
                📍 Whole book · {bookStates.length === 1 ? bookStates[0] : `${bookStates.length} states (${bookStates.join(', ')})`}
              </span>
            )}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={()=>setShowImport(s=>!s)}
            style={{ padding:'7px 14px', border:'1px solid #c8d8c0', borderRadius:7, background:'#f4f8f0',
              color:'#3a6e32', fontSize:12, fontWeight:700, cursor:'pointer' }}>
            {showImport ? '▲ Hide Import' : '⬆ Import Data'}
          </button>
          <button onClick={loadYields} style={{ padding:'7px 12px', border:'1px solid #c8d8c0', borderRadius:7,
            background:'#f4f8f0', color:'#4a6e4a', fontSize:12, cursor:'pointer' }}>↺</button>
        </div>
      </div>

      {/* Import panel */}
      {showImport && (
        <div style={{ background:'#f8fcf8', border:'1px solid #d0e4cc', borderRadius:10, padding:16, marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:13, color:'#1a3a10', marginBottom:8 }}>Import {CROP_YEAR} Production Data</div>
          <div style={{ fontSize:12, color:'#5a7a5a', marginBottom:12, lineHeight:1.5 }}>
            <strong>NAU:</strong> Upload <em>Prior_Year_Net_Production.xlsx</em> from NAU AgencyPortal<br/>
            <strong>R&H:</strong> Upload the <em>Total Production Report PDF</em> from Rain & Hail (per agent)
          </div>
          <label style={{ display:'inline-block', padding:'8px 16px', background:'#1a3a10', color:'#fff',
            borderRadius:7, fontSize:12, fontWeight:700, cursor:'pointer', marginRight:10 }}>
            {importing ? '⏳ Processing…' : '📂 Choose File (.xlsx or .pdf)'}
            <input type="file" accept=".xlsx,.xls,.pdf" onChange={handleFile} disabled={importing}
              style={{ display:'none' }} />
          </label>
          {importMsg && (
            <div style={{ marginTop:10, padding:'8px 14px', borderRadius:6, fontSize:12, fontWeight:600,
              background: importMsg.ok ? '#eef6ec' : '#fdecea',
              color: importMsg.ok ? '#1a7a1a' : '#c0392b',
              border: `1px solid ${importMsg.ok ? '#7a9a7a' : '#e07070'}` }}>
              {importMsg.text}
            </div>
          )}

          {/* Manual match queue */}
          {matchQueue.length > 0 && matchIdx < matchQueue.length && (
            <div style={{ marginTop:14, padding:14, background:'#fffbee', border:'1px solid #e8d090', borderRadius:8 }}>
              <div style={{ fontWeight:700, fontSize:13, color:'#6a4a00', marginBottom:6 }}>
                Manual Match ({matchIdx+1} of {matchQueue.length})
              </div>
              <div style={{ fontSize:12, color:'#4a3a00', marginBottom:8 }}>
                <strong>{matchQueue[matchIdx].farmer_name_raw}</strong> · Policy {matchQueue[matchIdx].policy_number} ·{' '}
                {matchQueue[matchIdx].crop} / {matchQueue[matchIdx].county} · {Number(matchQueue[matchIdx].acres).toFixed(1)} ac
              </div>
              <select value={matchedFarmer} onChange={e=>setMatchedFarmer(e.target.value)}
                style={{ padding:'7px 10px', border:'1px solid #d0c090', borderRadius:6, fontSize:12,
                  background:'#fff', minWidth:240, marginRight:8 }}>
                <option value=''>— Select farmer —</option>
                {(farmers||[]).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(f=>(
                  <option key={f.id} value={f.name}>{f.name}</option>
                ))}
              </select>
              <button onClick={()=>handleManualMatch(false)}
                style={{ padding:'7px 14px', background:'#1a3a10', color:'#fff', border:'none',
                  borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer', marginRight:6 }}>
                ✓ Match
              </button>
              <button onClick={()=>handleManualMatch(true)}
                style={{ padding:'7px 14px', background:'#fff', color:'#6a4a00', border:'1px solid #d0c090',
                  borderRadius:6, fontSize:12, cursor:'pointer' }}>
                Skip (import without match)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Crop selector tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:14 }}>
        {DISPLAY_CROPS.map(crop => (
          <button key={crop} onClick={()=>{ setSelectedCrop(crop); setExpandedCounty(null); }}
            style={{ flex:1, padding:'9px 4px', borderRadius:20,
              border:`2px solid ${selectedCrop===crop ? cropColor[crop] : '#d0d8c8'}`,
              background: selectedCrop===crop ? cropColor[crop] : '#fff',
              color: selectedCrop===crop ? '#fff' : '#5a7a5a',
              fontWeight:700, fontSize:13, cursor:'pointer', transition:'all 0.15s' }}>
            {crop === 'SOYBEANS' ? 'Soybeans' : crop.charAt(0)+crop.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'40px 0', color:'#60735f', fontSize:14 }}>Loading yield data…</div>
      ) : allCropRows.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px 0', color:'#9a9a8a', fontSize:13 }}>
          No {CROP_YEAR} {selectedCrop.toLowerCase()} yield data. Use Import to load NAU Excel or R&H PDF.
        </div>
      ) : (
        <>
          {/* Book-wide summary */}
          <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
            {/* Book Average + Farmers + Counties — mini row on mobile */}
            {isMobile ? (
              <div style={{ display:'flex', gap:8, width:'100%' }}>
                {[
                  { label:'Book Avg', value: fmtYield(bookAvg), color: cc },
                  { label:'Farmers',  value: `${totalFarmers}`,   color:'#4a6e4a' },
                  { label:'Counties', value: `${counties.length}`,color:'#4a6e4a' },
                ].map(k => (
                  <div key={k.label} style={{ flex:1, padding:'8px 6px', background:cb, border:`1px solid ${cc}44`, borderRadius:8, textAlign:'center' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:cc, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>{k.label}</div>
                    <div style={{ fontSize:14, fontWeight:800, color:k.color, fontVariantNumeric: "tabular-nums" }}>{k.value}</div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div style={{ flex:'1 1 120px', padding:'10px 16px', background:cb, border:`1px solid ${cc}44`, borderRadius:8, textAlign:'center' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:cc, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Book Average</div>
                  <div style={{ fontSize:20, fontWeight:800, color:cc, fontVariantNumeric: "tabular-nums" }}>{fmtYield(bookAvg)}</div>
                </div>
                <div style={{ flex:'1 1 100px', padding:'10px 16px', background:cb, border:`1px solid ${cc}44`, borderRadius:8, textAlign:'center' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:cc, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Farmers</div>
                  <div style={{ fontSize:20, fontWeight:800, color:'#4a6e4a', fontVariantNumeric: "tabular-nums" }}>{totalFarmers}</div>
                </div>
                <div style={{ flex:'1 1 100px', padding:'10px 16px', background:cb, border:`1px solid ${cc}44`, borderRadius:8, textAlign:'center' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:cc, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Counties</div>
                  <div style={{ fontSize:20, fontWeight:800, color:'#4a6e4a', fontVariantNumeric: "tabular-nums" }}>{counties.length}</div>
                </div>
              </>
            )}
            {/* Acres Reported — full width card, no progress bar */}
            <div style={{ flex:'1 1 100%', padding: isMobile?'10px 12px':'10px 16px', background: cb,
              border:`1px solid ${cc}44`, borderRadius:8 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                <div style={{ fontSize:10, fontWeight:700, color:cc, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                  Acres Reported
                </div>
                <div style={{ fontSize:14, fontWeight:800, fontVariantNumeric: "tabular-nums", color:'#4a6e4a' }}>
                  {fmtAcres(totalAcres)} ac
                </div>
              </div>
            </div>
          </div>

          {/* County table */}
          <div style={{ border:'1px solid #dde8d8', borderRadius:10, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f4f8f0' }}>
                  {(isMobile ? [
                    { key:'county', label:'County',    align:'left'  },
                    { key:'avg',    label:'Yield',     align:'right' },
                    { key:'ac',     label:'Acres',     align:'right' },
                    { key:null,     label:'',          align:'center'},
                  ] : [
                    { key:'county',      label:'County',       align:'left'  },
                    { key:'avg',         label:'Wtd Avg Yield',align:'right' },
                    { key:'ac',          label:'Total Acres',  align:'right' },
                    { key:'farmerCount', label:'Farmers',      align:'right' },
                    { key:null,          label:'AIP Split',    align:'right' },
                    { key:null,          label:'',             align:'center'},
                  ]).map(col => (
                    <th key={col.label} onClick={col.key ? ()=>toggleSort(col.key) : undefined}
                      style={{ padding: isMobile?'10px 10px':'9px 14px', textAlign:col.align, fontSize:10, fontWeight:800,
                        color: sortKey===col.key ? '#1a5a1a' : '#5a7a5a',
                        textTransform:'uppercase', letterSpacing:'0.06em',
                        borderBottom:'2px solid #d8e8d0',
                        cursor: col.key ? 'pointer' : 'default',
                        userSelect:'none',
                        background: sortKey===col.key ? '#ecf5ec' : undefined,
                        whiteSpace:'nowrap' }}>
                      {col.label}{col.key && <SortArrow col={col.key}/>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {counties.map((county, ci) => {
                  const rows = byCounty[county];
                  const avg  = weightedAvg(rows);
                  const ac   = rows.reduce((s,r)=>s+Number(r.acres||0),0);
                  const nauRows = rows.filter(r=>r.aip==='NAU');
                  const rhRows  = rows.filter(r=>r.aip==='RH');
                  const nauAc   = nauRows.reduce((s,r)=>s+Number(r.acres||0),0);
                  const rhAc    = rhRows.reduce((s,r)=>s+Number(r.acres||0),0);
                  const expanded = expandedCounty === county;

                  const farmerYields = {};
                  rows.forEach(r => {
                    const { id: fid, name: fname } = resolveFarmer(r);
                    if (!farmerYields[fid]) farmerYields[fid] = { name: fname, rows: [] };
                    farmerYields[fid].rows.push(r);
                  });
                  const farmerList = Object.values(farmerYields)
                    .map(f => ({ name: f.name, avg: weightedAvg(f.rows), acres: f.rows.reduce((s,r)=>s+Number(r.acres||0),0), aip: f.rows[0]?.aip }))
                    .sort((a,b) => (b.avg||0)-(a.avg||0));

                  const P = isMobile ? '10px 10px' : '10px 14px';
                  return (
                    <React.Fragment key={county}>
                      <tr style={{ background: ci%2===0 ? '#fff' : '#fafcf8',
                        borderBottom: expanded ? 'none' : '1px solid #edf2e8',
                        cursor:'pointer' }}
                        onClick={()=>setExpandedCounty(expanded ? null : county)}>
                        <td style={{ padding:P, fontWeight:700, color:'#1a2e1a', fontSize: isMobile?12:undefined, whiteSpace: isMobile?'nowrap':undefined, maxWidth: isMobile?'110px':undefined, overflow:'hidden', textOverflow:'ellipsis' }}>
                          {county.charAt(0)+county.slice(1).toLowerCase()} Co.
                        </td>
                        <td style={{ padding:P, textAlign:'right', fontVariantNumeric: "tabular-nums",
                          fontWeight:800, fontSize: isMobile?14:15, color: cc }}>
                          {fmtYield(avg)}
                        </td>
                        {isMobile ? (
                          // On mobile: acres + expand arrow in same cell
                          <td style={{ padding:P, textAlign:'right', whiteSpace:'nowrap' }}>
                            <span style={{ fontVariantNumeric: "tabular-nums", color:'#4a6e4a', fontSize:12 }}>{fmtAcres(ac)}</span>
                            <span style={{ marginLeft:6, color:'#4a7a4a', fontSize:11 }}>{expanded?'▲':'▼'}</span>
                          </td>
                        ) : (
                          <>
                            <td style={{ padding:P, textAlign:'right', fontVariantNumeric: "tabular-nums", color:'#4a6e4a' }}>
                              {fmtAcres(ac)}
                            </td>
                            <td style={{ padding:P, textAlign:'right', color:'#6a8a6a', fontSize:12 }}>
                              {farmerList.length || '—'}
                            </td>
                            <td style={{ padding:P, textAlign:'right', fontSize:11, color:'#60735f' }}>
                              {nauAc>0 && <span style={{marginRight:4}}>NAU {fmtAcres(nauAc)}</span>}
                              {rhAc>0  && <span>R&H {fmtAcres(rhAc)}</span>}
                            </td>
                            <td style={{ padding:P, textAlign:'center', color:'#4a7a4a', fontSize:13 }}>
                              {expanded ? '▲' : '▼'}
                            </td>
                          </>
                        )}
                      </tr>
                      {expanded && (
                        <tr style={{ background:'#f0f6ec', borderBottom:'1px solid #d4e8d0' }}>
                          <td colSpan={isMobile?3:6} style={{ padding: isMobile?'0':'0 0 0 24px' }}>
                            {isMobile ? (
                              /* Mobile drill-down: card list instead of table */
                              <div style={{ padding:'8px 10px', display:'flex', flexDirection:'column', gap:6 }}>
                                {farmerList.map((f,fi) => (
                                  <div key={fi} style={{ background: fi%2===0?'#f8fcf6':'#f0f6ec', borderRadius:6, padding:'8px 10px',
                                    display:'flex', justifyContent:'space-between', alignItems:'center', border:'1px solid #ddecd8' }}>
                                    <div>
                                      <div style={{ fontWeight:700, color:'#1a3a10', fontSize:12 }}>{f.name}</div>
                                      <div style={{ fontSize:11, color:'#6a8a6a', marginTop:1 }}>
                                        {fmtAcres(f.acres)}
                                        <span style={{ marginLeft:8, fontSize:10, fontWeight:700, padding:'1px 5px', borderRadius:3,
                                          background: f.aip==='NAU'?'#e8f0fa':'#fdf0e8',
                                          color: f.aip==='NAU'?'#1a4a7a':'#7a3a10' }}>
                                          {f.aip==='RH'?'R&H':f.aip||'—'}
                                        </span>
                                      </div>
                                    </div>
                                    <div style={{ fontVariantNumeric: "tabular-nums", fontWeight:800, color:cc, fontSize:15 }}>
                                      {fmtYield(f.avg)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                              <thead>
                                <tr style={{ background:'#e8f0e4' }}>
                                  <th style={{ padding:'7px 12px', textAlign:'left', fontWeight:700,
                                    color:'#3a5a30', fontSize:10, textTransform:'uppercase' }}>Farmer</th>
                                  <th style={{ padding:'7px 12px', textAlign:'right', fontWeight:700,
                                    color:'#3a5a30', fontSize:10, textTransform:'uppercase' }}>Yield</th>
                                  <th style={{ padding:'7px 12px', textAlign:'right', fontWeight:700,
                                    color:'#3a5a30', fontSize:10, textTransform:'uppercase' }}>Acres</th>
                                  <th style={{ padding:'7px 12px', textAlign:'right', fontWeight:700,
                                    color:'#3a5a30', fontSize:10, textTransform:'uppercase' }}>Production</th>
                                  <th style={{ padding:'7px 12px', textAlign:'left', fontWeight:700,
                                    color:'#3a5a30', fontSize:10, textTransform:'uppercase' }}>AIP</th>
                                </tr>
                              </thead>
                              <tbody>
                                {farmerList.map((f,fi) => (
                                  <tr key={fi} style={{ borderBottom:'1px solid #ddecd8',
                                    background: fi%2===0 ? '#f8fcf6' : '#f0f6ec' }}>
                                    <td style={{ padding:'7px 12px', fontWeight:700, color:'#1a3a10' }}>{f.name}</td>
                                    <td style={{ padding:'7px 12px', textAlign:'right', fontVariantNumeric: "tabular-nums",
                                      fontWeight:800, color: cc }}>
                                      {fmtYield(f.avg)}
                                    </td>
                                    <td style={{ padding:'7px 12px', textAlign:'right', fontVariantNumeric: "tabular-nums",
                                      color:'#4a6e4a' }}>
                                      {fmtAcres(f.acres)}
                                    </td>
                                    <td style={{ padding:'7px 12px', textAlign:'right', fontVariantNumeric: "tabular-nums",
                                      color:'#6a8a6a' }}>
                                      {(f.acres && f.avg) ? `${Math.round(f.acres*f.avg).toLocaleString()} bu` : '—'}
                                    </td>
                                    <td style={{ padding:'7px 12px' }}>
                                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 6px',
                                        borderRadius:4, background: f.aip==='NAU'?'#e8f0fa':'#fdf0e8',
                                        color: f.aip==='NAU'?'#1a4a7a':'#7a3a10',
                                        border: `1px solid ${f.aip==='NAU'?'#1a4a7a':'#7a3a10'}33` }}>
                                        {f.aip==='RH'?'R&H':f.aip||'—'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background:'#eef6ea', fontWeight:800, borderTop:'2px solid #c8e0c0' }}>
                  <td style={{ padding: isMobile?'10px 10px':'10px 14px', color:'#1a2e1a', fontWeight:800, fontSize: isMobile?13:undefined }}>Book Total</td>
                  <td style={{ padding: isMobile?'10px 10px':'10px 14px', textAlign:'right', fontVariantNumeric: "tabular-nums",
                    fontWeight:800, fontSize: isMobile?14:15, color: cc }}>{fmtYield(bookAvg)}</td>
                  <td style={{ padding: isMobile?'10px 10px':'10px 14px', textAlign:'right', fontVariantNumeric: "tabular-nums",
                    color:'#2a5a2a', fontWeight:800, fontSize: isMobile?12:undefined }}>{fmtAcres(totalAcres)}</td>
                  {!isMobile && <td style={{ padding:'10px 14px', textAlign:'right', color:'#4a6a4a' }}>{totalFarmers}</td>}
                  {!isMobile && <td colSpan={2}></td>}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Export button — full width on mobile */}
          <div style={{ marginTop:10 }}>
            <button onClick={exportToExcel}
              style={{ padding:'10px 18px', background:'#1a2e1a', color:'#fff', border:'none',
                borderRadius:7, fontSize:12, fontWeight:700, cursor:'pointer',
                width: isMobile?'100%':undefined,
                display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              ⬇ Export to Excel
            </button>
          </div>
        </>
      )}
    </div>
  );
}


function SortableTable({ columns, rows, defaultSortKey, defaultSortDir, renderRow, accent, footer }) {
  const [sortKey, setSortKey] = React.useState(defaultSortKey || columns[0]?.key);
  const [sortDir, setSortDir] = React.useState(defaultSortDir || 'asc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = [...rows].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    // Handle Date objects
    if (av instanceof Date) av = av.getTime();
    if (bv instanceof Date) bv = bv.getTime();
    // Handle strings
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av == null) av = sortDir === 'asc' ? Infinity : -Infinity;
    if (bv == null) bv = sortDir === 'asc' ? Infinity : -Infinity;
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const thStyle = (col) => ({
    padding:"8px 12px",
    textAlign: col.align || "left",
    fontSize:10, fontWeight:800, letterSpacing:"0.08em",
    color: accent || "#9a7a40",
    textTransform:"uppercase",
    borderBottom:"1px solid #e8e0d0",
    background:"#f8f4ec",
    cursor:"pointer",
    userSelect:"none",
    whiteSpace:"nowrap",
  });

  const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';

  return (
    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
      <thead><tr>
        {columns.map(col => (
          <th key={col.key} style={thStyle(col)} onClick={() => handleSort(col.key)}>
            {col.label}<span style={{ fontSize:10, opacity: sortKey === col.key ? 1 : 0.3 }}>{arrow(col.key)}</span>
          </th>
        ))}
      </tr></thead>
      <tbody>
        {sorted.map((row, i) => renderRow(row, i))}
      </tbody>
      {footer && <tfoot>{footer}</tfoot>}
    </table>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// PRF GRID MAP — interactive statewide rainfall-index dashboard
// A touch-first NY map of all PRF grids colored by rainfall / index / trigger
// status / book exposure, with a live coverage-level slider, a sortable grid
// table (driest-first by default), and a tap-to-open detail sheet. Built for
// the meeting touch screen — tap a grid to see its rainfall story.
//
// Data sources:
//   • prf_grid_locations  — grid geometry (exact min/max lat/lon box bounds)
//   • prf_grid_indices    — per-interval rainfall + index (FINAL or ESTIMATED)
//   • prf_grid_daily_precip — interval-to-date accumulation (fetched on tap)
//   • the `farmers` prop  — book overlay (prfPolicies[].intervals[]); no joins
//
// Index values are on a percent scale (100 = normal); coverage is a percent
// here for the slider (70–90). A grid "triggers" when index < coverage.
// ─────────────────────────────────────────────────────────────────────────────
const PRF_INTERVALS = [
  { code: "JANFEB", label: "Jan–Feb", m0: 0,  m1: 1  },
  { code: "FEBMAR", label: "Feb–Mar", m0: 1,  m1: 2  },
  { code: "MARAPR", label: "Mar–Apr", m0: 2,  m1: 3  },
  { code: "APRMAY", label: "Apr–May", m0: 3,  m1: 4  },
  { code: "MAYJUN", label: "May–Jun", m0: 4,  m1: 5  },
  { code: "JUNJUL", label: "Jun–Jul", m0: 5,  m1: 6  },
  { code: "JULAUG", label: "Jul–Aug", m0: 6,  m1: 7  },
  { code: "AUGSEP", label: "Aug–Sep", m0: 7,  m1: 8  },
  { code: "SEPOCT", label: "Sep–Oct", m0: 8,  m1: 9  },
  { code: "OCTNOV", label: "Oct–Nov", m0: 9,  m1: 10 },
  { code: "NOVDEC", label: "Nov–Dec", m0: 10, m1: 11 },
];
// ELEVEN intervals, not twelve. RMA's Rainfall Index runs Jan–Feb through Nov–Dec
// and there is no Dec–Jan: its public index API returns interval codes 625–635 and
// nothing else, no Dec–Jan normal is published for any grid, no Dec–Jan index has
// ever settled, and not one policy in the book insures one. A twelfth entry here
// only offered a choice that could never render anything, which reads as missing
// data rather than as an interval the program does not have.

// ── States this map covers ───────────────────────────────────────────────────
// A state is listed here once we hold BOTH halves for it: RMA's actuarials
// (prf_county_base_values / prf_premium_rates, loaded from the ADM) and grid
// geometry (prf_grid_locations). `fips` selects the counties out of the us-atlas
// topojson AND the RMA state code in prf_grid_states; `abbr` is what sob_data
// stores. `rotate` is the transverse-Mercator [central meridian, latitude origin]
// — one per state, because NY's rotation smears a map of Michigan.
const PRF_MAP_STATES = [
  { fips: "36", abbr: "NY", label: "New York",     rotate: [76.0, -40.0] },
  { fips: "26", abbr: "MI", label: "Michigan",     rotate: [85.0, -41.5] },
  { fips: "39", abbr: "OH", label: "Ohio",         rotate: [82.5, -39.0] },
  { fips: "42", abbr: "PA", label: "Pennsylvania", rotate: [77.5, -39.5] },
];
const PRF_DEFAULT_FIPS = "36"; // the book's home state, and the fallback when nothing else resolves

// A PRF grid id IS its NOAA CPC raster index — 0.25° cells, latitude origin
// 20.125N, longitude origin 230.125E, 300 columns. api/m13-sync.js runs this
// formula FORWARDS to turn a field's lat/lon into a grid id (prfGridFromLatLon);
// this is the same formula inverted, so the box is derived, not invented.
//
// It is checked rather than trusted: sql/032 ABORTS its geometry insert unless
// the inverse reproduces every row prf_grid_locations already holds, and on
// 2026-08-19 it reproduced all 264 exactly — centre latitude, centre longitude
// and all four bounds. A stored row still wins below; this only fills the gaps.
//
// Why derive at all: prf_grid_locations holds whatever has been loaded (264 rows
// — 251 New York grids, plus 13 out-of-state ones added by hand because the book
// insures them), while RMA's actuarials list 285 / 360 / 215 / 244 for
// NY / MI / OH / PA. Reading geometry only from that table draws Michigan as
// seven boxes floating on a state outline, which looks like a state with seven
// PRF grids rather than a table we have not finished loading.
// The lattice comes from src/prf/prfGrid.js, which owns it for the whole repo —
// the forward direction (lat/lon → grid) used to live in both syncs and the
// inverse lived here, so the same 0.25° raster was written down four times.
// The PRF Advisor needs both directions on the client, and a fifth copy is how
// two screens come to disagree about which grid a field is in.
// Which prf_grid_normals vintage answers for a crop year. RMA moved the Rainfall
// Index precipitation source from NOAA CPC to NOAA NCEI nClimGrid effective CROP
// YEAR 2027, and sql/058 keys the table on (grid_id, interval_code, methodology)
// so both can be stored. An UNSCOPED read returns two rows per grid/interval once
// the NCEI normals land and silently takes whichever arrives first — a coin flip
// between two denominators that differ by more than the thing being measured
// (median 4.7 index points across the book's own grids, measured 2026-09-03).
// Mirrors methodologyForCropYear() in src/prf/prfIndexMethodology.js; App.jsx
// imports nothing, so it is restated here and the checker asserts the two agree.
const PRF_NORMAL_FIRST_NCEI_CROP_YEAR = 2027;
function prfNormalMethodology(cropYear) {
  const y = Number(cropYear);
  // Unknown year -> the vintage of record TODAY. Never a blend, never both.
  if (!Number.isFinite(y)) return "CPC_LEGACY";
  return y >= PRF_NORMAL_FIRST_NCEI_CROP_YEAR ? "NCEI_MONTHLY" : "CPC_LEGACY";
}

const { PRF_CPC_STEP, prfGridBox, prfGridFromLatLon, prfGridCentre } = PRF_GRID;
const prfStateByFips = (f) => PRF_MAP_STATES.find((s) => s.fips === String(f)) || PRF_MAP_STATES[0];
// A farmer's PRF state, read off the POLICY (policies.state, which the AIP file
// sets) — never off prf_grid_locations.state_code. A 0.25° grid box straddles
// state lines, so 47 grids belong to two states in RMA's own actuarials and the
// single-column answer is wrong for them: Bridgewater's OHIO policy sits on grid
// 25982, which that column labels Michigan. Returns null when the book says
// nothing, so the caller can fall back rather than assert a state.
function prfBookStateFips(farmers) {
  const count = {};
  for (const f of farmers || []) {
    for (const p of (f.prfPolicies || [])) {
      const st = PRF_MAP_STATES.find((s) => s.abbr === String(p.state || "").toUpperCase());
      if (!st) continue;
      // Weight by intervals so a farm's real book, not a stub policy row, decides.
      count[st.fips] = (count[st.fips] || 0) + Math.max(1, (p.intervals || []).length);
    }
  }
  const best = Object.keys(count).sort((a, b) => count[b] - count[a])[0];
  return best || null;
}

// Trigger gradient — red→green diverging on the COVERAGE line (not 100% normal,
// which is what the rain view centers on). The red side is shaded by payout depth
// (payment factor = (coverage − index)/coverage = share of liability paid); the
// green side by how safely above the line. Re-centers live as you drag coverage.
// Open-interval color: rain so far against the normal for the SAME days. No
// red, because nothing triggers until the interval closes (Kevin, 2026-11-02).
const PRF_TODATE_LEGEND = [["#c9b48a", "under 50%"], ["#ddd0ae", "50–75%"], ["#ecebdc", "75–100%"], ["#cfe0ea", "100–125%"], ["#a3c4da", "over 125%"]];
function prfToDateColor(pct) {
  if (pct == null || !Number.isFinite(pct)) return "#e6ebe6";
  if (pct < 50) return PRF_TODATE_LEGEND[0][0];
  if (pct < 75) return PRF_TODATE_LEGEND[1][0];
  if (pct < 100) return PRF_TODATE_LEGEND[2][0];
  if (pct < 125) return PRF_TODATE_LEGEND[3][0];
  return PRF_TODATE_LEGEND[4][0];
}
function prfTriggerColor(indexPct, covPct) {
  if (indexPct == null || !Number.isFinite(indexPct)) return "#e9e9e4";
  if (indexPct >= covPct) {                     // safe — green by margin above the line
    const m = indexPct - covPct;
    if (m > 25) return "#3f8f3f";
    if (m > 10) return "#7bb56a";
    return "#cfe6c0";
  }
  const pf = (covPct - indexPct) / covPct;      // pays — red/orange by share of liability
  if (pf < 0.05) return "#f0913a";
  if (pf < 0.15) return "#e3713c";
  if (pf < 0.28) return "#cc3a1e";
  return "#7e1212";
}
// Sequential green for book exposure (acres or liability), scaled to the max.
function prfBookColor(val, max) {
  if (!val || !max || val <= 0) return "#f3f5f1";
  const t = Math.min(1, val / max);
  if (t < 0.12) return "#dfead9";
  if (t < 0.28) return "#bcd6b0";
  if (t < 0.50) return "#8fbd83";
  if (t < 0.75) return "#5a9e58";
  return "#2f7a34";
}

// `explorer` turns the same map into the PRF Advisor's Grid Explorer: the map,
// the state picker, the tinting and the pan/zoom are all identical, and the only
// difference is an extra full-width panel under it. A second map component would
// be a second answer to "where is this grid", which is the duplication this
// module was just consolidated to avoid.
// ── THE PRF LATTICE OVER A LEAFLET MAP — ONE DEFINITION ────────────────
//
// Two surfaces draw it now: the prospect quoter, where it says which cell the
// ground being drawn will be assigned to, and the Grid Explorer, where finding
// the cell IS the job. A second copy is how the two come to disagree about
// which grid a field is in — the failure this file already records for the
// forward/inverse formula being written down four times.
//
// It draws the cells whose edges are on screen and RETURNS the cell the map
// centre sits in; the caller renders that, because of the trap below.
function prfDrawLattice(L, map, group, opts = {}) {
  const { minZoom = 8, maxCells = 400, color = "#2a6499" } = opts;
  if (!L || !map || !group) return null;
  group.clearLayers();
  // ⚠ AT FIELD ZOOM THE GRID IS INVISIBLE, AND IT IS NOT MISSING. A PRF cell is
  // 0.25 degrees — about 17 miles across — so once you zoom in far enough to see
  // a field, the cell edges are off screen and nothing draws. It reads as "the
  // grids are not showing". What makes the lattice legible at EVERY zoom is
  // naming the cell the map CENTRE sits in, which is why that is returned
  // whether or not a boundary happens to be visible.
  const c = map.getCenter();
  const centre = PRF_GRID.prfGridFromLatLon(c.lat, c.lng) || null;
  if (map.getZoom() < minZoom) return centre;
  const b = map.getBounds();
  const g0 = PRF_GRID.prfGridFromLatLon(b.getSouth(), b.getWest());
  const g1 = PRF_GRID.prfGridFromLatLon(b.getNorth(), b.getEast());
  if (!g0 || !g1) return centre;
  const r0 = Math.floor((g0 - 1) / 300), r1 = Math.floor((g1 - 1) / 300);
  const c0 = (g0 - 1) % 300, c1 = (g1 - 1) % 300;
  if ((Math.abs(r1 - r0) + 1) * (Math.abs(c1 - c0) + 1) > maxCells) return centre;
  for (let r = Math.min(r0, r1); r <= Math.max(r0, r1); r++) {
    for (let cc = Math.min(c0, c1); cc <= Math.max(c0, c1); cc++) {
      const id = r * 300 + cc + 1, box = PRF_GRID.prfGridBox(id);
      const style = opts.styleFor ? opts.styleFor(id) : null;
      L.rectangle([[box.minLat, box.minLon], [box.maxLat, box.maxLon]], {
        color, weight: 1, opacity: 0.7, fill: false, interactive: false, ...(style || {}),
      }).addTo(group);
      // ⚠ THE LABEL GOES IN THE MIDDLE OF ITS CELL, NOT THE CORNER. Drawn at the
      // north-west corner it sits ON the shared boundary of four cells, so the
      // numbers near the bottom of the viewport belong to cells further south —
      // the map read 27511/27512/27513 beside an exposure list saying
      // 27811/27812/27810. Exactly one row apart (300), both correct, and
      // unmistakably a bug to anyone looking at it. A map whose labels appear to
      // disagree with the numbers beside it is worse than a map with no labels.
      const mid = { lat: (box.minLat + box.maxLat) / 2, lon: (box.minLon + box.maxLon) / 2 };
      const tint = opts.labelFor ? opts.labelFor(id) : null;
      L.marker([mid.lat, mid.lon], { interactive: false, icon: L.divIcon({
        className: "", iconSize: [64, 16], iconAnchor: [32, 8],
        html: `<span style="font:700 11px system-ui;color:${(tint && tint.color) || "#1b3a5c"};` +
              `background:${(tint && tint.bg) || "rgba(255,255,255,.82)"};padding:1px 4px;border-radius:3px">` +
              `${(tint && tint.text) || id}</span>`,
      }) }).addTo(group);
    }
  }
  return centre;
}
function PRFGridMapView({ farmers, isMobile, mode = "admin", explorer = false, onStartQuote = null }) {
  const portal = mode === "portal"; // farmer portal: scope the table to their own grids only
  // AGENCY CHROME vs THE VIEWER'S OWN BOOK — two questions that were one flag
  // until 2026-08-21, when this stopped being a Markets tab and became a page
  // the customer reaches directly.
  //
  // `internal` is the AGENT's meeting-mode switch: it hides the agency book so
  // the map can go up on a screen in front of a room. A customer has no room to
  // hide their own acres from, so that switch is agency chrome and is never
  // rendered for them — which means `internal` must NOT be what decides whether
  // a customer sees their own data. `showBook` is that decision, and in portal
  // it is unconditionally true. Gating book data on `internal` directly is the
  // bug this pair exists to prevent: change its default and the customer's page
  // silently empties.
  //
  // The numbers were always theirs — `farmers` is portalFarmers, their own farms
  // and nothing else. What portal drops is the agency VOICE: "Our acres", "Grids
  // we're in", "grids with our customers", the policy count. Kevin, 2026-08-21:
  // a portal user must not be looking at a button that says "Our acres".
  // (`showBook` itself is declared below, beside the `internal` state it reads —
  // a const is not hoisted-initialised, so declaring it up here is a TDZ crash.)
  const [d3Lib, setD3Lib] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [locs, setLocs] = useState([]);          // [{ grid_id, minLat, maxLat, minLon, maxLon }]
  const [indices, setIndices] = useState([]);    // raw prf_grid_indices rows
  const [loading, setLoading] = useState(true);

  const [cropYear, setCropYear] = useState(new Date().getFullYear());
  const [interval, setIntervalCode] = useState("MAYJUN");
  // Which state's grid map is on screen. Seeded from the viewer's own PRF policy
  // (see prfBookStateFips) so a Michigan farm opens on Michigan, not on New York.
  // `stateTouched` records that a HUMAN chose — once they have, the book default
  // must never yank the map back, which is what an unguarded effect would do.
  const [stateFips, setStateFips] = useState(() => prfBookStateFips(farmers) || PRF_DEFAULT_FIPS);
  const stateTouched = useRef(false);
  const [gridStates, setGridStates] = useState([]); // [{ grid_id, state_code }] — many-to-many, 47 grids are in two states
  const [gridNormals, setGridNormals] = useState([]); // [{ grid_id, interval_code, normal_inches }] — the %-of-normal denominator
  const [metric, setMetric] = useState("trigger");  // trigger | acres
  const [coverage, setCoverage] = useState(85);  // % — the slider; trigger line
  const [internal, setInternal] = useState(true);// false = public/meeting mode (hide book)
  // ⚠ THE GRID EXPLORER IS NOT THE BOOK REVIEW, AND WITHOUT THIS THEY WERE THE
  // SAME SCREEN. Grid Explorer answers "what does RMA publish for this grid" —
  // reference data, no customer. Left as-is it inherited the whole book overlay
  // ("Grids we're in · 26 · $8.7M", Our acres, the liability and policy-count
  // columns, the insured-grid highlight), so two of the Advisor's four entry
  // points rendered near-identically and the agency's book sat on a screen whose
  // entire premise is that no customer is involved. That also makes it the wrong
  // thing to have open in front of a prospect.
  const showBook = !explorer && (portal || internal);
  // A grid can be deep-linked into the Explorer — the same key a future "open
  // this grid" link would carry, and what lets a QA run land on a selection
  // rather than on an empty map.
  const [selectedGrid, setSelectedGrid] = useState(() => {
    if (!explorer) return null;
    try { const g = Number(sessionStorage.getItem("cloverag_prf_explorer_grid")); return g > 0 ? g : null; } catch { return null; }
  });
  const [expandedGrid, setExpandedGrid] = useState(null); // table row expanded to show per-farm payment breakdown
  const [insuredIvlOnly, setInsuredIvlOnly] = useState(false); // table filter: only grids we insure in the SELECTED interval
  const [sortKey, setSortKey] = useState("index");
  const [sortDir, setSortDir] = useState("asc"); // driest-first by default
  const [gridOpacity, setGridOpacity] = useState(0.5);    // translucency of the grid wash
  const [showCounties, setShowCounties] = useState(true); // county name labels (find-your-farm reference)
  const [productivity, setProductivity] = useState(1.0);  // PRF productivity factor (0.6–1.5), editable
  const [cbvByGrid, setCbvByGrid] = useState({});         // grid_id → { [interval]: {hay, graze} } RMA County Base Values
  // Rate rows per grid, as fetched — NOT a collapsed map. See the fetch below.
  const [prfRatesByGrid, setPrfRatesByGrid] = useState({});
  // ⚠ THE RATE YEAR IS NOT THE INDEX YEAR, AND CONFLATING THEM IS WHY 2027 COULD
  // NOT BE QUOTED. `cropYear` above is the INDEX year and its options are built
  // from index HISTORY, so CY2027 can never appear there — that year has not run
  // and carries 0 `prf_grid_indices` rows against 6,037 for CY2026. Rates are the
  // opposite kind of thing: RMA publishes them BEFORE the year starts, and all 39
  // book grids already carry CY2027 (880 rate rows on grid 27808 against 165 for
  // CY2026 — the eight-band shape lookupPrfRate() exists to narrow).
  //
  // BOTH years are fetched in ONE request and partitioned here, so switching the
  // year cannot race a refetch. That is load-bearing: the fetch effect keys on
  // `selectedGrid` alone, so a `crop_year=eq.${prfRateYear}` in the query with no
  // year in the deps would serve LAST year's rates under this year's label —
  // silently, on a customer-visible premium.
  // Defaults to this calendar year (a PRF crop year IS a calendar year), so the page
  // rolls without an edit; the picker offers it and the next.
  const [prfRateYear, setPrfRateYear] = useState(new Date().getFullYear());
  // The years this page will quote. A year is OFFERED only where the selected
  // grid actually returned rates for it, so the picker can never name a year
  // whose premium would come back blank.
  const PRF_RATE_YEARS = [new Date().getFullYear(), new Date().getFullYear() + 1];   // this year and the next one RMA has rated
  // The intended use and practice band a premium refers to. Null means "not
  // chosen", which renders as a range rather than as a number.
  const [prfUse, setPrfUse] = useState(null);
  const [prfPractice, setPrfPractice] = useState(null);
  const [prfRateCounty, setPrfRateCounty] = useState(null);// grid_id → { '${interval}|${covDecimal}': base_rate } CY2026 gross premium rates
  const [histFrom, setHistFrom] = useState(2017);         // historical analysis year range (final indices)
  const [histTo, setHistTo] = useState(new Date().getFullYear());

  // ── Full-screen + pan/zoom (touch-screen friendly: pinch, drag, wheel) ──
  const VB_W = 800, VB_H = 520;
  const [fullscreen, setFullscreen] = useState(false);
  const [view, setView] = useState({ x: 0, y: 0, w: VB_W, h: VB_H });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const suppressClickRef = useRef(false);
  const resetView = () => setView({ x: 0, y: 0, w: VB_W, h: VB_H });
  const zoomAt = (px, py, factor) => setView((v) => {
    const cx = v.x + px * v.w, cy = v.y + py * v.h;
    const nw = Math.max(120, Math.min(VB_W, v.w * factor)), nh = nw * (VB_H / VB_W);
    return { x: cx - px * nw, y: cy - py * nh, w: nw, h: nh };
  });
  const onWheel = (e) => { const r = e.currentTarget.getBoundingClientRect(); zoomAt((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height, e.deltaY < 0 ? 0.82 : 1.22); };
  const onMouseDown = (e) => { dragRef.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y, moved: false }; };
  const onMouseMove = (e) => {
    const d = dragRef.current; if (!d) return;
    const r = e.currentTarget.getBoundingClientRect();
    if (Math.abs(e.clientX - d.sx) + Math.abs(e.clientY - d.sy) > 3) d.moved = true;
    setView((v) => ({ ...v, x: d.vx - (e.clientX - d.sx) / r.width * v.w, y: d.vy - (e.clientY - d.sy) / r.height * v.h }));
  };
  const onMouseUp = () => { if (dragRef.current && dragRef.current.moved) suppressClickRef.current = true; dragRef.current = null; };
  const touchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 1) { dragRef.current = { sx: e.touches[0].clientX, sy: e.touches[0].clientY, vx: view.x, vy: view.y, moved: false }; pinchRef.current = null; }
    else if (e.touches.length === 2) { pinchRef.current = { d: touchDist(e.touches), w: view.w }; dragRef.current = null; }
  };
  const onTouchMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (e.touches.length === 2 && pinchRef.current) {
      const startW = pinchRef.current.w;  // capture before the async updater (pinchRef may clear)
      const factor = pinchRef.current.d / Math.max(1, touchDist(e.touches));
      const mx = ((e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left) / r.width;
      const my = ((e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top) / r.height;
      setView((v) => { const cx = v.x + mx * v.w, cy = v.y + my * v.h; const nw = Math.max(120, Math.min(VB_W, startW * factor)), nh = nw * (VB_H / VB_W); return { x: cx - mx * nw, y: cy - my * nh, w: nw, h: nh }; });
      suppressClickRef.current = true;
    } else if (e.touches.length === 1 && dragRef.current) {
      const d = dragRef.current, t = e.touches[0];
      if (Math.abs(t.clientX - d.sx) + Math.abs(t.clientY - d.sy) > 4) d.moved = true;
      setView((v) => ({ ...v, x: d.vx - (t.clientX - d.sx) / r.width * v.w, y: d.vy - (t.clientY - d.sy) / r.height * v.h }));
    }
  };
  const onTouchEnd = () => { if ((dragRef.current && dragRef.current.moved) || pinchRef.current) suppressClickRef.current = true; dragRef.current = null; pinchRef.current = null; };
  const onGridClick = (gid) => { if (suppressClickRef.current) { suppressClickRef.current = false; return; } setSelectedGrid((g) => g === gid ? null : gid); };

  const [dailyByGrid, setDailyByGrid] = useState({}); // grid_id → [{date, mm}] full crop-year (selected grid)
  const [intervalRain, setIntervalRain] = useState({}); // grid_id → { inches, days } accumulated this interval, from daily precip
  const [dailyAsOf, setDailyAsOf] = useState(null);     // latest obs_date in prf_grid_daily_precip (true data freshness)
  const [dailyFromDate, setDailyFromDate] = useState(null); // earliest obs_date — intervals before this fall back to stored estimate
  const [lossRatioByCounty, setLossRatioByCounty] = useState({}); // county → { year, ratio } from RMA SOB (most recent settled year)

  // Grid ids belonging to the selected state, out of RMA's own actuarials
  // (prf_grid_states, built from the ADM). EVERY state-scoped fetch narrows on
  // this, and that is load-bearing rather than tidy: the index history and the
  // daily-precip rollup are both book-wide reads that run past PostgREST's
  // ceilings once four states of grids exist (~130k index rows against sbFetch's
  // 50k cap; ~68k precip rows for one interval). Unscoped, they truncate silently
  // and the map reports a short read as a fact about the weather.
  //
  // `membershipReady && no rows` means the ADM membership hasn't been loaded in
  // this environment — fall back to UNSCOPED, i.e. exactly today's behaviour,
  // rather than rendering an empty map.
  const [membershipReady, setMembershipReady] = useState(false);
  const scopeByState = membershipReady && gridStates.length > 0;
  const stateGridSet = useMemo(() => {
    const s = new Set();
    for (const r of gridStates) if (String(r.state_code) === String(stateFips)) s.add(Number(r.grid_id));
    return s;
  }, [gridStates, stateFips]);
  const stateGridIds = useMemo(() => [...stateGridSet].sort((a, b) => a - b), [stateGridSet]);
  const stateDef = prfStateByFips(stateFips);
  // The grids actually on screen: EVERY grid RMA lists in this state, not only the
  // ones prf_grid_locations has been loaded with. Everything downstream — shapes,
  // the table, the summary strip, "N of M grids triggering" — counts THIS list, so
  // an out-of-state grid can no longer be tallied as one of the state's own (the
  // header read "264 NY grids" when 13 of them were in Michigan and Pennsylvania).
  //
  // A STORED row always wins; prfGridBox() only fills a grid the table does not
  // carry yet. The two are verified identical for all 264 stored rows, so this
  // cannot put a box somewhere the loaded geometry would have put it differently.
  const stateLocs = useMemo(() => {
    if (!scopeByState) return locs;
    const stored = new Map();
    for (const g of locs) stored.set(Number(g.grid_id), g);
    const out = [];
    for (const gid of stateGridSet) {
      const hit = stored.get(gid);
      if (hit) { out.push(hit); continue; }
      const box = prfGridBox(gid);
      if (box) out.push({ grid_id: String(gid), ...box, derived: true });
    }
    return out.sort((a, b) => Number(a.grid_id) - Number(b.grid_id));
  }, [locs, stateGridSet, scopeByState]);

  // Seed the map from the viewer's own book once `farmers` resolves. The lazy
  // useState above runs on the FIRST render, when the portal's farmer list is
  // still loading and every book-derived default reads as "no book" — so without
  // this the Michigan farm still opens on New York. Guarded by stateTouched: a
  // human's pick outranks the book, always.
  useEffect(() => {
    if (stateTouched.current) return;
    const fips = prfBookStateFips(farmers);
    if (fips) setStateFips((cur) => (cur === fips ? cur : fips));
  }, [farmers]);

  // ── Load geometry + d3 + grid membership (state-independent, once) ──
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      d3Promise,
      fetch("https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json").then((r) => r.json()).catch(() => null),
      sbFetch("prf_grid_locations", { select: "grid_id,min_lat,max_lat,min_lon,max_lon" }).catch(() => []),
      sbFetch("prf_grid_states", { select: "grid_id,state_code" }).catch(() => []),
      sbFetch("prf_grid_daily_precip", { select: "obs_date", order: "obs_date.desc", limit: 1 }).catch(() => []),
      sbFetch("prf_grid_daily_precip", { select: "obs_date", order: "obs_date.asc", limit: 1 }).catch(() => []),
    ]).then(([d3, topo, locRows, stateRows, dailyLatest, dailyEarliest]) => {
      if (cancelled) return;
      if (dailyLatest && dailyLatest[0]?.obs_date) setDailyAsOf(dailyLatest[0].obs_date);
      if (dailyEarliest && dailyEarliest[0]?.obs_date) setDailyFromDate(dailyEarliest[0].obs_date);
      if (d3) setD3Lib(d3);
      // Keep every US county and pick the state's out in a memo — one CDN fetch
      // serves all four states, so switching states costs nothing.
      // decodeTopo returns a plain ARRAY of features, NOT a FeatureCollection —
      // wrap it, or `geoData.features` is undefined, stateGeo is null, and the map
      // renders zero counties and zero grid boxes while every count above it stays
      // correct. A green build and eight static checkers all passed on that; only
      // driving the real page in a browser caught it.
      if (topo) setGeoData({ type: "FeatureCollection", features: decodeTopo(topo, topo.objects.counties) });
      setGridStates(stateRows || []);
      setMembershipReady(true);
      setLocs((locRows || []).map((g) => ({
        grid_id: String(g.grid_id),
        minLat: Number(g.min_lat), maxLat: Number(g.max_lat),
        minLon: Number(g.min_lon), maxLon: Number(g.max_lon),
      })).filter((g) => Number.isFinite(g.minLat) && Number.isFinite(g.minLon)));
    }).catch(() => { if (!cancelled) setMembershipReady(true); });
    return () => { cancelled = true; };
  }, []);

  // ── Load the selected state's index history + county loss ratios ──
  // Refetched per state, and narrowed to that state's grids. `id.asc` is the
  // stable sort Range paging needs — an unordered multi-page read drops and
  // repeats rows across page edges.
  useEffect(() => {
    if (!membershipReady) return;
    let cancelled = false;
    setLoading(true);
    const gridFilter = scopeByState ? [`grid_id=in.(${stateGridIds.join(",")})`] : [];
    Promise.all([
      sbFetch("prf_grid_indices", {
        select: "grid_id,crop_year,interval_code,estimated_index_value,estimated_rainfall_inches,normal_rainfall_inches,normal_to_date_inches,final_index_value,final_rainfall_inches,is_final,estimated_as_of",
        filters: gridFilter,
        order: "id.asc",
      }).catch(() => []),
      sbFetch("sob_data", { select: "county,crop_year,premium,indemnity", filters: ["crop=eq.PRF", `state=eq.${stateDef.abbr}`] }).catch(() => []),
      // ⚠ THE MAP FETCHES NORMALS ONCE PER STATE, BUT ITS CROP YEAR IS SEPARATE
      // STATE THAT CHANGES WITHOUT REFETCHING — so unlike the two quote paths
      // there is no single crop year here to scope by. It is pinned to the
      // vintage of record TODAY rather than left unscoped, because unscoped
      // becomes a silent coin flip the moment NCEI normals load.
      // FOLLOW-UP, and it is a PREREQUISITE of loading those normals: carry
      // the methodology through to the %-of-normal lookup and select on the
      // displayed crop year. Until then this map is correct for CY2026 and
      // earlier and must not be read for CY2027.
      sbFetch("prf_grid_normals", {
        select: "grid_id,interval_code,normal_inches,methodology",
        filters: [...gridFilter, "methodology=eq.CPC_LEGACY"],
      }).catch(() => []),
    ]).then(([idxRows, sobPrf, normalRows]) => {
      if (cancelled) return;
      // County PRF loss ratio from RMA Summary of Business — use the most recent
      // year that actually settled (indemnity booked); the current CY shows $0.
      // Only NY is loaded today, so the other states render no loss-ratio tint —
      // absent, which is the honest answer, not zero.
      const yrIndem = {};
      for (const r of sobPrf || []) { const y = Number(r.crop_year); yrIndem[y] = (yrIndem[y] || 0) + (Number(r.indemnity) || 0); }
      const settledYear = Math.max(0, ...Object.keys(yrIndem).filter((y) => yrIndem[y] > 0).map(Number));
      const lr = {};
      if (settledYear) {
        for (const r of sobPrf || []) {
          if (Number(r.crop_year) !== settledYear) continue;
          const c = String(r.county || "").toUpperCase(); const prem = Number(r.premium) || 0;
          if (prem > 0) lr[c] = { year: settledYear, ratio: (Number(r.indemnity) || 0) / prem };
        }
      }
      setLossRatioByCounty(lr);
      setGridNormals(normalRows || []);
      setIndices(idxRows || []);
      const years = (idxRows || []).map((r) => Number(r.crop_year)).filter(Boolean);
      if (years.length) setCropYear((cur) => (years.includes(Number(cur)) ? cur : Math.max(...years)));
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [membershipReady, scopeByState, stateGridIds, stateDef.abbr]);

  // ── Index lookup: `${grid_id}|${interval_code}` → effective values ──
  // Prefers the RMA FINAL when published, else the running NOAA estimate.
  const idxByGridInterval = useMemo(() => {
    const m = {};
    for (const r of indices) {
      if (Number(r.crop_year) !== Number(cropYear)) continue;
      const isFinal = !!r.is_final && r.final_index_value != null;
      const normal = r.normal_rainfall_inches != null ? Number(r.normal_rainfall_inches) : null;
      let index = isFinal ? Number(r.final_index_value) : (r.estimated_index_value != null ? Number(r.estimated_index_value) : null);
      let rain;
      if (isFinal) {
        // RMA's final ADM feed carries the index but NOT rainfall inches, so derive
        // inches from index × normal (exact), falling back to our NOAA estimate.
        //
        // ⚠ THE STORED INCHES ARE PREFERRED BECAUSE THEY ARE MORE PRECISE, NOT BECAUSE
        // THEY ARE MORE AUTHORITATIVE. The INDEX is what RMA settles on; the inches are
        // a separate fetch from a separate service, and on 2026-09-01 that service
        // changed its measurement unit and wrote every MAY-JUN 2026 grid at a tenth of
        // the real rainfall. Diamond D read "0.59\" (95%)" against a 6.29" normal —
        // the index right, the trigger right, and only the headline figure wrong, which
        // is exactly why nobody could see it. So a stored figure that CONTRADICTS the
        // index on its own row is not used: the index wins and the inches are derived
        // from it, which is the fallback that already sat on the next line.
        // Thresholds measured over the 25,806 settled rows carrying all three — the
        // worst legitimate disagreement (RMA publishes the index rounded) is 16% and
        // 1.45", and the unit defect sits at 90%. They are duplicated from
        // DST_BAD_ABS/DST_BAD_REL in api/_prf-index.js and check-prf-rainfall-unit
        // asserts the two copies agree.
        const shown = prfFinalInches(r.final_rainfall_inches, index, normal);
        rain = shown != null ? shown
             : (r.estimated_rainfall_inches != null ? Number(r.estimated_rainfall_inches) : null);
      } else {
        rain = r.estimated_rainfall_inches != null ? Number(r.estimated_rainfall_inches) : null;
      }
      // Sanity guard: an exactly-0 index/rain over a 2-month interval is physically
      // impossible in NY — it's a failed per-grid estimate roll-up (see grid 27211
      // APRMAY 2026), not a real drought. Treat exact 0 (non-final) as no-data so a
      // single sync gap can't masquerade as a deep trigger and dominate the sort.
      if (!isFinal && normal > 0 && (index === 0 || rain === 0)) { index = null; rain = null; }
      const normalToDate = r.normal_to_date_inches != null ? Number(r.normal_to_date_inches) : null;
      m[`${r.grid_id}|${r.interval_code}`] = { index, rain, normal, normalToDate, isFinal, asOf: r.estimated_as_of };
    }
    return m;
  }, [indices, cropYear]);

  // ── Book overlay derived from the farmers prop (no extra fetch) ──
  // Per grid: insured acres (one value per grid/farmer — acres repeat across a
  // grid's intervals, so we take the max not the sum), liability (sums across
  // intervals), policy count, customer names + counties + coverage levels.
  const bookByGrid = useMemo(() => {
    const out = {};
    for (const f of farmers || []) {
      // ⚠ ONE CROP YEAR ONLY, AND IT IS THE YEAR IN FORCE — see prfCoverageState(). Liability SUMS across a
      // grid's intervals here, so two crop years doubled every grid's book
      // liability, while the acres max() silently took whichever year was
      // larger rather than this year's.
      // The year is the MAP's crop year, not today's: a 2025 map over the 2026
      // book paired one year's rain with another year's acres and liability.
      for (const p of prfCoverageState(f.prfPolicies, cropYear).policies) {
        const cov = Number(p.coverageLevel) || 0.9; // coverage actually bought
        const perGrid = {};
        for (const iv of (p.intervals || [])) {
          const gid = String(iv.gridId || iv.grid_id || "");
          if (!gid) continue;
          if (!perGrid[gid]) perGrid[gid] = { acres: 0, liab: 0, county: iv.county || null, byInterval: {} };
          perGrid[gid].acres = Math.max(perGrid[gid].acres, Number(iv.insuredAcres) || 0);
          perGrid[gid].liab += Number(iv.liability) || 0;
          if (!perGrid[gid].county && iv.county) perGrid[gid].county = iv.county;
          // Per-interval buckets. PRF liability is split per interval by % value, so a
          // single interval's payout must use THAT interval's liability, not the annual
          // sum. `base` = liability ÷ coverage-bought is the coverage-independent
          // protection, so the slider models true coverage what-ifs:
          // indemnity = base × max(0, coverage − index).
          // `valAcres` = acres × %value (value-allocated acres). PRF pays FULL value
          // on these fewer acres, so per-acre coverage = liability ÷ valAcres (the
          // RMA County Base Value × productivity × coverage). Dividing by raw acres
          // dilutes it by %value and understates coverage.
          const code = parsePRFCode(iv.intervalCode || iv.interval_code);
          if (code) {
            const liab = Number(iv.liability) || 0;
            const pv = Number(iv.percentValue) || Number(iv.percent_value) || 0;
            const b = perGrid[gid].byInterval[code] || (perGrid[gid].byInterval[code] = { liab: 0, acres: 0, valAcres: 0, base: 0 });
            b.liab += liab; b.acres += Number(iv.insuredAcres) || 0; b.valAcres += (Number(iv.insuredAcres) || 0) * pv; b.base += liab / cov;
          }
        }
        for (const gid of Object.keys(perGrid)) {
          if (!out[gid]) out[gid] = { acres: 0, liab: 0, policies: 0, customers: [], counties: new Set(), covLevels: new Set(), byInterval: {} };
          out[gid].acres += perGrid[gid].acres;
          out[gid].liab += perGrid[gid].liab;
          out[gid].policies += 1;
          // Keep per-policy coverage + per-interval buckets on the customer entry so the
          // table's grid dropdown can compute each farm's potential payment for the
          // selected interval (indemnity = base × max(0, cov − index)).
          out[gid].customers.push({ name: f.name || "—", acres: perGrid[gid].acres, liab: perGrid[gid].liab, cov, byInterval: perGrid[gid].byInterval });
          for (const code of Object.keys(perGrid[gid].byInterval)) {
            const src = perGrid[gid].byInterval[code];
            const dst = out[gid].byInterval[code] || (out[gid].byInterval[code] = { liab: 0, acres: 0, valAcres: 0, base: 0 });
            dst.liab += src.liab; dst.acres += src.acres; dst.valAcres += src.valAcres; dst.base += src.base;
          }
          if (perGrid[gid].county) out[gid].counties.add(String(perGrid[gid].county).toUpperCase());
          if (p.coverageLevel != null) out[gid].covLevels.add(Number(p.coverageLevel));
        }
      }
    }
    return out;
  }, [farmers, cropYear]);

  const bookMaxAcres = useMemo(() => Math.max(1, ...Object.values(bookByGrid).map((b) => b.acres || 0)), [bookByGrid]);

  // ── The selected state's counties, out of the one nationwide topojson ──
  const stateGeo = useMemo(() => {
    if (!geoData) return null;
    const features = (geoData.features || []).filter((f) => String(f.properties.fips || "").startsWith(stateDef.fips));
    return features.length ? { type: "FeatureCollection", features } : null;
  }, [geoData, stateDef.fips]);

  // ── Projection (fit to the selected state) ──
  const projection = useMemo(() => {
    if (!stateGeo || !d3Lib) return null;
    return d3Lib.geoTransverseMercator().rotate(stateDef.rotate).fitSize([800, 520], stateGeo);
  }, [stateGeo, d3Lib, stateDef.rotate]);
  const pathGen = useMemo(() => (projection && d3Lib ? d3Lib.geoPath().projection(projection) : null), [projection, d3Lib]);

  // Normal rainfall is climatology (year-invariant), but the ADM final backfill
  // didn't carry it for historical years. Reuse any year's normal per grid+interval
  // so historical "received"/"normal" inches populate (received = index × normal).
  // The %-of-normal DENOMINATOR, and without it a grid has no index at all.
  //
  // prf_grid_normals is the source: RMA's own expected value, back-calculated from
  // its published Final Grid Index (normal = actual ÷ index) by
  // tools/prf-normals.mjs. prf_grid_indices.normal_rainfall_inches is the FALLBACK,
  // and only a fallback, because the server rollup that writes it
  // (computeEstimatedIndices in noaa-cpc-sync) is deliberately scoped to the ~37
  // grids the book insures — every other grid's copy is whatever the last unscoped
  // run left behind. Reading the table directly is what lets a state we do not yet
  // insure in show a real index instead of grey.
  //
  // Normal is climatology: year-invariant, so one lookup serves every crop year.
  const normalByGI = useMemo(() => {
    const m = {};
    for (const r of gridNormals) {
      const v = Number(r.normal_inches);
      if (Number.isFinite(v) && v > 0) m[`${r.grid_id}|${r.interval_code}`] = v;
    }
    for (const r of indices) {
      if (r.normal_rainfall_inches == null) continue;
      const k = `${r.grid_id}|${r.interval_code}`;
      if (m[k] == null) m[k] = Number(r.normal_rainfall_inches);
    }
    return m;
  }, [gridNormals, indices]);

  // ── Per-grid rows for the current interval (drives both map shapes + table) ──
  const rows = useMemo(() => {
    const prog = progressForInterval(interval);
    // Daily precip only exists from dailyFromDate (~Apr 2026). Intervals that
    // start before that can't be computed from daily — fall back to the stored
    // estimate (stable for those completed past intervals). RMA finals always win.
    const covers = dailyCoversInterval(interval);
    // COVERAGE GUARD. The running index is rain-to-date over the FULL-interval
    // normal, so it only means anything once the daily feed actually covers the
    // elapsed window. A grid that joined the feed part-way through an interval
    // carries a handful of days against a two-month normal — a ~2% index, drawn as
    // the deepest red on the map, for a grid that may have had a wet spring. That
    // is not hypothetical: it is what happens to every grid the day after its
    // geometry is loaded. Require 70% of the elapsed days (the same threshold
    // noaa-cpc-sync applies server-side) or report no index at all.
    const ivDef = PRF_INTERVALS.find((i) => i.code === interval);
    let minDays = 1;
    if (ivDef) {
      const startMs = Date.UTC(cropYear, ivDef.m0, 1);
      const endMs = Date.UTC(ivDef.m1 < ivDef.m0 ? cropYear + 1 : cropYear, ivDef.m1 + 1, 0);
      const capMs = Math.min(dailyAsOf ? Date.parse(dailyAsOf + "T00:00:00Z") : Date.now(), endMs);
      const elapsedDays = Math.max(1, Math.round((capMs - startMs) / 86400000) + 1);
      minDays = Math.max(1, Math.floor(elapsedDays * 0.7));
    }
    // Closed AND the daily feed reaches its last day: rain ÷ full normal is real.
    const ivWin = PRF_IS.intervalWindow(interval, cropYear);
    const ivEndStr = ivWin ? new Date(ivWin.end).toISOString().slice(0, 10) : null;
    const dailyClosedThrough = !!(ivEndStr && dailyAsOf && dailyAsOf >= ivEndStr);
    const out = [];
    for (const g of stateLocs) {
      const ix = idxByGridInterval[`${g.grid_id}|${interval}`] || null;
      const bk = bookByGrid[g.grid_id] || null;
      // normalByGI FIRST: it is prf_grid_normals, the authoritative table. ix.normal
      // is prf_grid_indices' copy, which is only refreshed for book grids.
      const normal = normalByGI[`${g.grid_id}|${interval}`] ?? ix?.normal ?? null;
      const isFinal = ix?.isFinal ?? false;
      // ⚠ AN INDEX EXISTS ONLY ONCE THE INTERVAL HAS CLOSED (Kevin, 2026-11-02).
      // Rain so far ÷ the FULL normal read every open interval as a trigger (58
      // of 139 in 2026). While open, the one number is % of normal TO DATE: rain
      // so far ÷ the normal for the same days. `index` stays null then, so the
      // trigger call, the summary and every payment below skip it on their own.
      let rain = null, index = null, toDateIdx = null, normalSoFar = null, reading = null;
      const dr = intervalRain[g.grid_id];
      const drCovers = dr != null && dr.days >= minDays;
      // The stored normal-to-date is a share of ITS normal; carry the share onto
      // the normal this page uses, and only when it was taken the same day.
      const ntdShare = (ix?.normalToDate > 0 && ix?.normal > 0) ? ix.normalToDate / ix.normal : null;
      if (!isFinal && covers && drCovers && normal > 0) {
        rain = dr.inches;
        if (dailyClosedThrough) {
          index = (rain / normal) * 100; reading = "EST";
        } else {
          normalSoFar = (ntdShare != null && ix?.asOf && dailyAsOf && String(ix.asOf).slice(0, 10) === dailyAsOf)
            ? normal * ntdShare
            : PRF_IS.normalToDate(interval, cropYear, { normalInches: normal, asOf: dailyAsOf });
          if (normalSoFar > 0) { toDateIdx = (rain / normalSoFar) * 100; reading = "SO_FAR"; }
        }
      } else if (ix) {
        // RMA final OR a pre-daily interval → stored value, through the same rule.
        const rd = PRF_IS.intervalReading(interval, cropYear, {
          finalValue: isFinal ? ix.index : null, estimatedValue: ix.index,
          estimatedInches: ix.rain, normalInches: normal,
          normalToDateInches: ntdShare != null && normal > 0 ? normal * ntdShare : null,
          estimatedAsOf: ix.asOf,
        });
        if (rd.index != null) {
          index = rd.index; reading = isFinal ? "FINAL" : "EST";
          // derive received inches from index × normal (historical years too)
          rain = normal ? index / 100 * normal : (ix.rain ?? null);
        } else if (rd.toDate != null) {
          toDateIdx = rd.toDate; reading = "SO_FAR"; rain = ix.rain ?? null;
          normalSoFar = rain != null && toDateIdx > 0 ? rain / (toDateIdx / 100) : null;
        }
      }
      out.push({
        ...g, normal, isFinal, complete: prog.complete, asOf: ix?.asOf ?? null,
        rain, index, toDateIdx, normalSoFar, reading,
        pct: index != null ? index / 100 : null,
        triggers: index != null ? index < coverage : null,
        acres: bk?.acres ?? 0,
        liab: bk?.liab ?? 0,
        policies: bk?.policies ?? 0,
        intervalLiab: bk?.byInterval?.[interval]?.liab ?? 0,
        intervalAcres: bk?.byInterval?.[interval]?.acres ?? 0,
        intervalValAcres: bk?.byInterval?.[interval]?.valAcres ?? 0,
        intervalBase: bk?.byInterval?.[interval]?.base ?? 0,
        county: bk ? Array.from(bk.counties)[0] : null,
        hasBook: !!bk,
      });
    }
    return out;
  }, [stateLocs, idxByGridInterval, normalByGI, interval, cropYear, coverage, bookByGrid, intervalRain, dailyAsOf, dailyFromDate]);

  const rowsByGrid = useMemo(() => {
    const m = {}; for (const r of rows) m[r.grid_id] = r; return m;
  }, [rows]);

  // ── Map shapes (project each grid's box once per projection) ──
  const shapes = useMemo(() => {
    if (!projection) return [];
    const out = [];
    for (const r of rows) {
      const corners = [
        [r.minLon, r.maxLat], [r.maxLon, r.maxLat],
        [r.maxLon, r.minLat], [r.minLon, r.minLat],
      ].map((c) => projection(c)).filter((p) => p && !isNaN(p[0]));
      if (corners.length !== 4) continue;
      out.push({
        grid_id: r.grid_id,
        pts: corners.map((p) => `${p[0]},${p[1]}`).join(" "),
        cx: (corners[0][0] + corners[2][0]) / 2,
        cy: (corners[0][1] + corners[2][1]) / 2,
        row: r,
      });
    }
    return out;
  }, [projection, rows]);

  // Public mode never colors by book data — fall back to the rainfall view even
  // if the metric state was left on a book metric when the toggle flipped.
  const effMetric = ((!internal || portal) && metric === "acres") ? "trigger" : metric;
  function fillFor(r) {
    if (effMetric === "acres") return prfBookColor(r.acres, bookMaxAcres);
    if (r.index == null && r.toDateIdx != null) return prfToDateColor(r.toDateIdx);
    return prfTriggerColor(r.index, coverage);
  }
  function labelFor(r) {
    if (effMetric === "acres")  return r.acres > 0 ? Math.round(r.acres) : "";
    const v = r.index ?? r.toDateIdx;
    return v != null ? Math.round(v) : "";
  }
  // The interval on screen is still open: grids read % of normal so far.
  const soFarMode = rows.some((r) => r.toDateIdx != null);

  // ── Sortable table rows ──
  const tableRows = useMemo(() => {
    // When the book is visible (portal OR internal/admin), the table is scoped to the
    // grids we actually insure (hasBook) — the map is the tool for exploring grids we're
    // not in; no sense listing all ~250 here. Public/meeting mode (internal off) has no
    // book overlay, so it keeps the full statewide list of grids with an index.
    let list = showBook
      ? rows.filter((r) => r.hasBook)
      : rows.filter((r) => r.index != null || r.toDateIdx != null);
    // Optional filter: only grids we insure in the CURRENTLY SELECTED interval (interval
    // liability > 0), dropping grids we cover in other intervals but not this one.
    if (insuredIvlOnly) list = list.filter((r) => r.intervalLiab > 0);
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (r) => {
      switch (sortKey) {
        case "grid":   return Number(r.grid_id);
        case "index":  return (r.index ?? r.toDateIdx) ?? (sortDir === "asc" ? 9e9 : -9e9);
        case "rain":   return r.rain ?? (sortDir === "asc" ? 9e9 : -9e9);
        case "acres":  return r.acres;
        case "liab":   return r.liab;
        case "policies": return r.policies;
        default: return 0;
      }
    };
    return [...list].sort((a, b) => (val(a) - val(b)) * dir);
  }, [rows, sortKey, sortDir, showBook, portal, insuredIvlOnly]);

  // ── Summary strip ──
  const summary = useMemo(() => {
    const withIdx = rows.filter((r) => r.index != null);
    const triggering = withIdx.filter((r) => r.triggers);
    const bookGrids = rows.filter((r) => r.hasBook);
    // Exposure is interval-specific: only grids we actually insure in THIS interval,
    // using that interval's acres/liability (not the annual footprint).
    const bookTriggering = bookGrids.filter((r) => r.triggers && r.intervalAcres > 0);
    const exposedAcres = bookTriggering.reduce((s, r) => s + r.intervalAcres, 0);
    const exposedLiab  = bookTriggering.reduce((s, r) => s + r.intervalLiab, 0);
    // PRF indemnity = base × max(0, coverage − index), where base = interval liability
    // ÷ coverage bought (coverage-independent protection), coverage/index in points.
    let projIndem = 0;
    for (const r of bookGrids) {
      if (r.index == null) continue;
      projIndem += Math.max(0, (coverage - r.index) / 100) * r.intervalBase;
    }
    const soFarBook = bookGrids.filter((r) => r.toDateIdx != null && r.intervalAcres > 0);
    const bookSoFarAvg = soFarBook.length ? soFarBook.reduce((s, r) => s + r.toDateIdx, 0) / soFarBook.length : null;
    return {
      bookSoFarAvg, bookSoFarGrids: soFarBook.length,
      gridsWithIdx: withIdx.length, triggering: triggering.length,
      bookGrids: bookGrids.length, bookTriggering: bookTriggering.length,
      exposedAcres, exposedLiab, projIndem,
      totalAcres: bookGrids.reduce((s, r) => s + r.acres, 0),
      totalLiab: bookGrids.reduce((s, r) => s + r.liab, 0),
    };
  }, [rows, coverage]);

  const dataAsOf = useMemo(() => {
    let final = false, asOf = null;
    for (const r of rows) {
      if (r.index == null && r.toDateIdx == null) continue;
      if (r.isFinal) final = true;
      if (r.asOf && (!asOf || r.asOf > asOf)) asOf = r.asOf;
    }
    return { final, asOf };
  }, [rows]);

  // How far the selected interval has elapsed. Mid-interval, the estimated index
  // compares partial rain to the FULL 2-month normal, so it reads artificially
  // low — a 43% MAYJUN index on June 5 is not "everyone's triggering", it's an
  // interval that's only ~half over. Surface that so the map isn't alarmist.
  function progressForInterval(code) {
    const iv = PRF_INTERVALS.find((i) => i.code === code);
    if (!iv) return { elapsed: 1, complete: true };
    const start = new Date(cropYear, iv.m0, 1);
    const endYear = iv.m1 < iv.m0 ? cropYear + 1 : cropYear;
    const end = new Date(endYear, iv.m1 + 1, 0, 23, 59, 59);
    // Anchor "now" to the latest daily-precip date so elapsed and rain-to-date are
    // measured at the same instant (avoids a slight pace under-read from data lag).
    const now = dailyAsOf ? new Date(dailyAsOf + "T23:59:59") : new Date();
    if (now >= end) return { elapsed: 1, complete: true };
    if (now <= start) return { elapsed: 0, complete: false, future: true };
    return { elapsed: (now - start) / (end - start), complete: false };
  }
  const intervalProgress = useMemo(() => progressForInterval(interval), [interval, cropYear, dailyAsOf]);

  // True if the daily-precip table covers an interval's full window (its first day
  // is on/after the earliest daily obs). If not, the interval predates the daily
  // data and must use the stored prf_grid_indices estimate instead.
  function dailyCoversInterval(code) {
    if (!dailyFromDate) return false;
    const iv = PRF_INTERVALS.find((i) => i.code === code);
    if (!iv) return false;
    return new Date(Date.UTC(cropYear, iv.m0, 1)).toISOString().slice(0, 10) >= dailyFromDate;
  }

  // ── Interval rainfall for the WHOLE map, summed straight from the daily-precip
  // table (fresh for every grid on screen), NOT from prf_grid_indices.estimated_*
  // — that rollup is intentionally scoped to ~37 book grids in noaa-cpc-sync, so
  // every other grid carries a stale estimate. The daily table is the source of
  // truth, so the map computes its own index and self-heals staleness/outliers.
  //
  // Narrowed to the SELECTED state's grids. Unscoped this is ~61 days times every
  // loaded grid — 16k rows for New York alone and ~68k across four states, past
  // sbFetch's 50k ceiling. A truncated read here does not fail loudly: it silently
  // under-sums rainfall, which renders as a drought.
  useEffect(() => {
    const iv = PRF_INTERVALS.find((i) => i.code === interval);
    if (!iv) return;
    if (scopeByState && !stateGridIds.length) { setIntervalRain({}); return; }
    let cancelled = false;
    const start = new Date(Date.UTC(cropYear, iv.m0, 1));
    const endYear = iv.m1 < iv.m0 ? cropYear + 1 : cropYear;
    const end = new Date(Date.UTC(endYear, iv.m1 + 1, 0));
    const fmtDate = (d) => d.toISOString().slice(0, 10);
    sbFetch("prf_grid_daily_precip", {
      select: "grid_id,precip_mm",
      filters: [`obs_date=gte.${fmtDate(start)}`, `obs_date=lte.${fmtDate(end)}`]
        .concat(scopeByState ? [`grid_id=in.(${stateGridIds.join(",")})`] : []),
      order: "id.asc", // STABLE sort — required: this fetch spans many pages, and
                        // and paginating without an order double-counts rows at page
                        // boundaries (inflated per-grid sums, e.g. a false 310% index).
    }).then((rws) => {
      if (cancelled) return;
      // Inches AND the day count behind them. The day count is what stops a grid
      // that only joined the precip feed last week from reading as a drought — see
      // the coverage guard in the rows memo.
      const sum = {};
      for (const r of rws || []) {
        const g = String(r.grid_id);
        const e = sum[g] || (sum[g] = { inches: 0, days: 0 });
        e.inches += (Number(r.precip_mm) || 0) / 25.4;
        e.days += 1;
      }
      setIntervalRain(sum);
    }).catch(() => { if (!cancelled) setIntervalRain({}); });
    return () => { cancelled = true; };
  }, [interval, cropYear, scopeByState, stateGridIds]);

  // ── On grid select: fetch that grid's FULL crop-year daily precip — drives the
  // accumulation sparkline AND the all-intervals strip (both fresh from daily). ──
  useEffect(() => {
    if (!selectedGrid) return;
    if (dailyByGrid[selectedGrid]) return;
    sbFetch("prf_grid_daily_precip", {
      select: "obs_date,precip_mm",
      filters: [`grid_id=eq.${selectedGrid}`, `obs_date=gte.${cropYear}-01-01`, `obs_date=lte.${cropYear + 1}-01-31`],
      order: "obs_date.asc",
    }).then((rws) => {
      setDailyByGrid((m) => ({ ...m, [selectedGrid]: (rws || []).map((r) => ({ date: r.obs_date, mm: Number(r.precip_mm) || 0 })) }));
    }).catch(() => setDailyByGrid((m) => ({ ...m, [selectedGrid]: [] })));
  }, [selectedGrid, cropYear]);

  // ── On grid select: fetch RMA County Base Values for this grid+year (the real,
  // program-fixed $/ac protection). Per interval, keep the top haying value
  // (type 030) and grazing (type 007) — what an agent quotes from. ──
  useEffect(() => {
    if (!selectedGrid) return;
    const key = `${selectedGrid}|${cropYear}`;
    if (cbvByGrid[key]) return;
    sbFetch("prf_county_base_values", {
      select: "interval_code,type_code,county_base_value",
      filters: [`grid_id=eq.${selectedGrid}`, `crop_year=eq.${cropYear}`],
    }).then((rws) => {
      const byIv = {};
      for (const r of rws || []) {
        const iv = r.interval_code, v = Number(r.county_base_value) || 0;
        if (!byIv[iv]) byIv[iv] = { hay: 0, graze: 0 };
        if (r.type_code === "030") byIv[iv].hay = Math.max(byIv[iv].hay, v);
        else if (r.type_code === "007") byIv[iv].graze = Math.max(byIv[iv].graze, v);
      }
      setCbvByGrid((m) => ({ ...m, [key]: byIv }));
    }).catch(() => setCbvByGrid((m) => ({ ...m, [key]: {} })));
  }, [selectedGrid, cropYear]);

  // ── On grid select: fetch CY2026 gross premium RATES for this grid (rate table
  // is published for 2026 only — keyed by interval|coverage). Drives premium-per-
  // acre + loss-ratio in the detail box. Rates are crop-year-fixed (2026), used
  // against historical final indices as a "loss ratio at current rates" read. ──
  useEffect(() => {
    if (!selectedGrid) return;
    if (prfRatesByGrid[selectedGrid]) return;
    // ⚠ KEEP THE ROWS. Collapsing them to `interval|coverage` is the defect:
    // 20,229 of 58,135 CY2026 cells carry TWO rates for that key (hay vs
    // grazing vs 997, and a grid straddles counties), so the map was letting
    // PostgREST row order decide a customer-visible premium. Worth $0.28/ac at
    // worst on 2026 and $3.96/ac on 2027. lookupPrfRate() narrows or refuses.
    sbFetch("prf_premium_rates", {
      select: PRF_RATE_SELECT,
      // BOTH quotable years in one request — see the note on prfRateYear. Never
      // put the year in this filter without also putting it in the deps below.
      filters: [`grid_id=eq.${selectedGrid}`, `crop_year=in.(${PRF_RATE_YEARS.join(",")})`],
    }).then((rws) => {
      setPrfRatesByGrid((p) => ({ ...p, [selectedGrid]: rws || [] }));
    }).catch(() => setPrfRatesByGrid((p) => ({ ...p, [selectedGrid]: [] })));
  }, [selectedGrid]);

  // PRF premium subsidy by coverage level (RMA): 70/75% → 59%, 80/85% → 55%, 90% → 51%.
  const prfSubsidy = (covPct) => (covPct <= 75 ? 0.59 : covPct <= 85 ? 0.55 : 0.51);

  const cropYears = useMemo(() => {
    const s = new Set(indices.map((r) => Number(r.crop_year)).filter(Boolean));
    return Array.from(s).sort((a, b) => b - a);
  }, [indices]);

  const selRow = selectedGrid ? rowsByGrid[selectedGrid] : null;
  const selIvIdx = PRF_INTERVALS.findIndex((i) => i.code === interval);
  const stepInterval = (d) => setIntervalCode(PRF_INTERVALS[(selIvIdx + d + PRF_INTERVALS.length) % PRF_INTERVALS.length].code);

  // Per-interval index for the selected grid's all-intervals strip — computed
  // fresh from its full crop-year daily precip (same source as the map), with
  // RMA finals preferred; in-progress shows the FLOOR (rain-to-date ÷ full normal).
  const selGridStrip = useMemo(() => {
    const out = {};
    if (!selectedGrid) return out;
    const daily = dailyByGrid[selectedGrid];
    for (const iv of PRF_INTERVALS) {
      const ix = idxByGridInterval[`${selectedGrid}|${iv.code}`] || null;
      // Same order as the rows memo: prf_grid_normals first, the prf_grid_indices
      // copy only as a fallback.
      const normal = normalByGI[`${selectedGrid}|${iv.code}`] ?? ix?.normal ?? null;
      const prog = progressForInterval(iv.code);
      if (prog.future) { out[iv.code] = { index: null, rain: null, normal, future: true }; continue; }
      // RMA final wins; intervals predating the daily data use the stored estimate.
      // Derive received inches from index × normal (fills historical Got column).
      if ((ix?.isFinal || !dailyCoversInterval(iv.code)) && ix?.index != null) {
        const rain = normal ? ix.index / 100 * normal : (ix.rain ?? null);
        out[iv.code] = { index: ix.index, rain, normal, complete: true, isFinal: ix.isFinal }; continue;
      }
      // Not final and no daily sum to use: the stored reading, through the same
      // closed-only rule as the map (an open interval reads % of normal so far).
      const stored = () => {
        const rd = PRF_IS.intervalReading(iv.code, cropYear, {
          estimatedValue: ix?.index, estimatedInches: ix?.rain, normalInches: normal,
          normalToDateInches: (ix?.normalToDate > 0 && ix?.normal > 0 && normal > 0) ? normal * ix.normalToDate / ix.normal : null,
          estimatedAsOf: ix?.asOf,
        });
        return { index: rd.index, toDate: rd.toDate, rain: ix?.rain ?? null, normal, complete: prog.complete, inProgress: rd.toDate != null };
      };
      if (!daily || !(normal > 0)) { out[iv.code] = stored(); continue; }
      const endY = iv.m1 < iv.m0 ? cropYear + 1 : cropYear;
      const s = new Date(Date.UTC(cropYear, iv.m0, 1)).toISOString().slice(0, 10);
      const e = new Date(Date.UTC(endY, iv.m1 + 1, 0)).toISOString().slice(0, 10);
      let sum = 0, any = false;
      for (const d of daily) { if (d.date >= s && d.date <= e) { sum += d.mm / 25.4; any = true; } }
      if (!any) { out[iv.code] = stored(); continue; }
      // Closed and the feed reaches its last day: rain ÷ full normal. Otherwise
      // % of normal so far, against the normal for the same days.
      if (dailyAsOf && dailyAsOf >= e) { out[iv.code] = { index: (sum / normal) * 100, rain: sum, normal, complete: true }; continue; }
      const ntd = PRF_IS.normalToDate(iv.code, cropYear, { normalInches: normal, asOf: dailyAsOf });
      out[iv.code] = { index: null, toDate: ntd > 0 ? (sum / ntd) * 100 : null, rain: sum, normal, complete: false, inProgress: true };
    }
    return out;
  }, [selectedGrid, dailyByGrid, idxByGridInterval, normalByGI, cropYear, dailyAsOf, dailyFromDate]);

  // Per-grid historical FINAL index by year for the selected interval (from the
  // 2017-2026 ADM backfill) — drives the year-range "how often it paid" analysis.
  const gridHistory = useMemo(() => {
    if (!selectedGrid) return [];
    const out = [];
    for (const r of indices) {
      if (String(r.grid_id) !== selectedGrid || r.interval_code !== interval) continue;
      if (!r.is_final || r.final_index_value == null) continue;
      out.push({ year: Number(r.crop_year), index: Number(r.final_index_value) });
    }
    return out.sort((a, b) => a.year - b.year);
  }, [indices, selectedGrid, interval]);

  const metricBtn = (key, label) => (
    <button onClick={() => setMetric(key)} style={{
      padding: "7px 12px", fontSize: 12, fontWeight: metric === key ? 700 : 500,
      color: metric === key ? "#fff" : "#3a5a3a", background: metric === key ? "#3a6e8a" : "#eef3ef",
      border: "1px solid " + (metric === key ? "#2d5570" : "#d6e0d6"), borderRadius: 7, cursor: "pointer",
    }}>{label}</button>
  );
  const th = (key, label, align) => (
    <th onClick={() => { if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortKey(key); setSortDir(key === "index" || key === "rain" ? "asc" : "desc"); } }}
      style={{ padding: "8px 10px", textAlign: align || "left", cursor: "pointer", whiteSpace: "nowrap", color: "#3a5a3a", fontWeight: 700, borderBottom: "2px solid #d6e0d6", userSelect: "none" }}>
      {label}{sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}>Loading PRF grid data…</div>;

  return (
    <div style={fullscreen
      ? { position: "fixed", inset: 0, zIndex: 3000, background: "#eef2ef", overflowY: "auto", padding: "12px 18px 40px" }
      : { marginTop: 12 }}>
      {/* ── Control bar ── */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <select value={stateFips} onChange={(e) => { stateTouched.current = true; setStateFips(e.target.value); setSelectedGrid(null); setExpandedGrid(null); resetView(); }}
          title="Which state's PRF grid map to show. Opens on the state your own PRF policy is written in."
          style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccd", fontSize: 13, fontWeight: 700 }}>
          {PRF_MAP_STATES.map((st) => <option key={st.fips} value={st.fips}>{st.label}</option>)}
        </select>
        <select value={cropYear} onChange={(e) => setCropYear(Number(e.target.value))} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccd", fontSize: 13 }}>
          {(cropYears.length ? cropYears : [cropYear]).map((y) => <option key={y} value={y}>CY{y}</option>)}
        </select>
        <select value={interval} onChange={(e) => setIntervalCode(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccd", fontSize: 13, fontWeight: 600 }}>
          {PRF_INTERVALS.map((iv) => <option key={iv.code} value={iv.code}>{iv.label}</option>)}
        </select>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {metricBtn("trigger", "🎯 Trigger status")}
          {/* Colouring the map by BOOK acres is an agency question ("where is
              our exposure"), and its label is in the agency's voice. A customer
              has one or two grids and no book to shade — dropped for portal. */}
          {!portal && !explorer && internal && metricBtn("acres", "🌾 Our acres")}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {/* Rainfall-feed freshness — latest observed daily precip across the
              loaded grids (NOAA CPC). Always shown so the data age is visible even
              on FINAL intervals; tints amber when the feed lags >4 days. */}
          {dailyAsOf && (() => {
            const d = new Date(dailyAsOf + "T12:00:00");
            const daysOld = Math.floor((Date.now() - d.getTime()) / 86400000);
            const fresh = daysOld <= 4;
            const label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
            return (
              <span title={`Rainfall data freshness: latest observed daily precipitation (NOAA CPC) feeding every grid's running index. ${daysOld <= 0 ? "Today" : daysOld + " day" + (daysOld === 1 ? "" : "s") + " ago"}.`}
                style={{ fontSize: 11, padding: "3px 8px", borderRadius: 10, fontWeight: 700, whiteSpace: "nowrap",
                  background: fresh ? "#e3f0e0" : "#fdf2dd", color: fresh ? "#2f6e2f" : "#8a6510" }}>
                🌧 Rainfall thru {label}{daysOld >= 1 ? ` · ${daysOld}d ago` : " · today"}
              </span>
            );
          })()}
          {!intervalProgress.complete && (
            <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 10, fontWeight: 700, background: "#eaf0f6", color: "#3a5a7a" }}>
              ⏳ {intervalProgress.future ? "Not started" : `In progress · ${Math.round(intervalProgress.elapsed * 100)}% elapsed`}
            </span>
          )}
          <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 10, fontWeight: 700,
            background: dataAsOf.final ? "#e3f0e0" : "#fdf2dd", color: dataAsOf.final ? "#2f6e2f" : "#8a6510" }}>
            {dataAsOf.final ? "● FINAL" : soFarMode ? "○ SO FAR" : "○ EST AT CLOSE"}{(dataAsOf.final ? dataAsOf.asOf : dailyAsOf) ? ` · ${dataAsOf.final ? dataAsOf.asOf : dailyAsOf}` : ""}
          </span>
          {/* Meeting mode. AGENT ONLY: it exists to blank the agency book before
              the map goes on a screen in a room, and "Internal" is not a word a
              customer should be reading about their own acres. */}
          {/* …and NOT on the Grid Explorer, which never draws the book, so the
              switch would be a control that visibly does nothing. */}
          {!portal && !explorer && (
          <button onClick={() => setInternal((v) => { const next = !v; if (!next && metric === "acres") setMetric("trigger"); return next; })} title="Toggle book overlay for public display" style={{
            padding: "6px 12px", fontSize: 12, fontWeight: 700, borderRadius: 7, cursor: "pointer",
            border: "1px solid " + (internal ? "#b05050" : "#4a7a4a"),
            color: internal ? "#fff" : "#2d4a2d", background: internal ? "#a04040" : "#e3f0e0",
          }}>{internal ? "🔒 Internal" : "👥 Public"}</button>
          )}
        </div>
      </div>

      {/* An empty map reads as "nothing is triggering here" — the opposite of the
          truth. Geometry is derived per grid now, so the only way to get here is
          that RMA's grid list for the state was never pulled. Name that. */}
      {membershipReady && stateLocs.length === 0 && (
        <div style={{ padding: "12px 14px", background: "#fdf2dd", border: "1px solid #e8d5a8", borderRadius: 8, marginBottom: 12, fontSize: 13, color: "#6b5010", lineHeight: 1.5 }}>
          <b>No PRF grids listed for {stateDef.label}.</b> RMA{"’"}s actuarials for {stateDef.abbr} have not been
          pulled, so <code style={{ margin: "0 4px" }}>prf_grid_states</code> carries no grid for it — run the ADM sync
          (<code>/api/market?fn=prf-index</code>) with {stateDef.abbr} in <code style={{ margin: "0 4px" }}>MAP_STATES</code>.
          This is a missing pull, not a state without PRF grids.
        </div>
      )}

      {/* How much of what is drawn is real data. A grid with no index is grey, not
          green — but grey is quiet, and a map that is mostly grey should say why
          rather than let it read as "nothing is triggering". */}
      {membershipReady && stateLocs.length > 0 && summary.gridsWithIdx < stateLocs.length && (
        <div style={{ padding: "9px 12px", background: "#eef3f7", border: "1px solid #d3e0ea", borderRadius: 8, marginBottom: 12, fontSize: 12.5, color: "#3a5a7a", lineHeight: 1.5 }}>
          {summary.gridsWithIdx} of {stateLocs.length} {stateDef.label} grids have an index for this interval.
          The remaining {stateLocs.length - summary.gridsWithIdx} are drawn but carry <b>no index</b> — RMA has not
          published a final for them and the rainfall feed has not been backfilled that far. No index is not a
          low index: those grids are grey, never red.
        </div>
      )}

      {/* ── Coverage slider ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 14px", background: "#f4f8f4", border: "1px solid #e0eae0", borderRadius: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a" }}>Coverage / trigger line</span>
        <input type="range" min={70} max={90} step={5} value={coverage} onChange={(e) => setCoverage(Number(e.target.value))} style={{ flex: 1, minWidth: 160, accentColor: "#3a6e8a", cursor: "pointer" }} />
        <span style={{ fontSize: 20, fontWeight: 800, color: "#3a6e8a", minWidth: 56, textAlign: "center" }}>{coverage}%</span>
        <span style={{ fontSize: 12, color: "#5a7258" }}>
          {soFarMode
            ? "Interval still open: grids show % of normal rain so far. Triggers and payments show once it closes."
            : `Grids below this index pay out. ${summary.triggering} of ${summary.gridsWithIdx} ${stateDef.abbr} grids triggering this interval.`}
        </span>
      </div>

      {/* ── Book summary strip — the agency book, or in portal their own ── */}
      {showBook && summary.bookGrids > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          {[
            { label: portal ? "Your grids" : "Grids we're in", val: summary.bookGrids, sub: `${Math.round(summary.totalAcres).toLocaleString()} ac · ${fmtD0(summary.totalLiab)}` },
            ...(soFarMode ? [
              { label: "Rain so far", val: summary.bookSoFarAvg != null ? Math.round(summary.bookSoFarAvg) + "%" : "—", sub: `of normal to date · ${summary.bookSoFarGrids} grid${summary.bookSoFarGrids === 1 ? "" : "s"}` },
              { label: "Interval", val: `${Math.round(intervalProgress.elapsed * 100)}% done`, sub: "triggers show at close" },
            ] : [
              { label: "Grids triggering", val: summary.bookTriggering, sub: `at ${coverage}% coverage`, hot: summary.bookTriggering > 0 },
              { label: "Acres exposed", val: Math.round(summary.exposedAcres).toLocaleString(), sub: `in triggering grids @ ${coverage}%`, hot: summary.exposedAcres > 0 },
            ]),
          ].map((c, i) => (
            <div key={i} style={{ flex: isMobile ? "1 1 0" : "1 1 150px", minWidth: 0, padding: isMobile ? "7px 9px" : "10px 14px", background: c.hot ? "#fbeceb" : "#fff", border: "1px solid " + (c.hot ? "#e0b4b0" : "#e0eae0"), borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: "#60735f", fontWeight: 600 }}>{c.label}</div>
              <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: c.hot ? "#a03028" : "#1a2e1a" }}>{c.val}</div>
              <div style={{ fontSize: isMobile ? 10 : 11, color: "#60735f", lineHeight: 1.3 }}>{c.sub}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* ── Map ── */}
        <div style={{ flex: "1 1 520px", minWidth: 300, position: "relative" }}>
          {/* zoom controls — big tap targets for the touch screen */}
          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 5, display: "flex", flexDirection: "column", gap: 4 }}>
            {[["＋", () => zoomAt(0.5, 0.5, 0.7)], ["－", () => zoomAt(0.5, 0.5, 1.43)], ["⟲", resetView], [fullscreen ? "✕" : "⛶", () => setFullscreen((f) => !f)]].map(([lbl, fn], i) => (
              <button key={i} onClick={fn} style={{ width: 34, height: 34, borderRadius: 7, border: "1px solid #cdd9e2", background: "rgba(255,255,255,0.92)", color: "#1a2e1a", fontSize: 16, fontWeight: 700, cursor: "pointer", lineHeight: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }}>{lbl}</button>
            ))}
          </div>
          <svg viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
            onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
            onDoubleClick={resetView} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
            style={{ width: "100%", height: fullscreen ? "calc(100vh - 230px)" : "auto", display: "block", background: "#dde7ee", borderRadius: 10, border: "1px solid #cdd9e2", cursor: dragRef.current ? "grabbing" : "grab", touchAction: "none" }}>
            {/* Base county fill (under the grids) */}
            {pathGen && stateGeo && (
              <g>
                {stateGeo.features.map((f, i) => (
                  <path key={i} d={pathGen(f)} fill="#eef2f0" stroke="#b9c8c4" strokeWidth={0.5} />
                ))}
              </g>
            )}
            {/* Grid heat fills — kept semi-transparent so the county lines drawn
                on top (and book-grid rings) read through instead of burying the
                geography. */}
            {shapes.map((s) => {
              const r = s.row;
              const isSel = selectedGrid === s.grid_id;
              return (
                <polygon key={s.grid_id} points={s.pts}
                  fill={fillFor(r)} fillOpacity={r.index == null && r.toDateIdx == null && metric !== "acres" && metric !== "liability" ? Math.min(0.2, gridOpacity * 0.35) : gridOpacity}
                  stroke={isSel ? "#10243a" : "rgba(0,0,0,0.12)"}
                  strokeWidth={isSel ? 2.4 : 0.4}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); onGridClick(s.grid_id); }}
                />
              );
            })}
            {/* County boundaries ON TOP of the heat fills — the orientation cue
                that was previously hidden under the opaque grids. */}
            {pathGen && stateGeo && (
              <g style={{ pointerEvents: "none" }}>
                {stateGeo.features.map((f, i) => (
                  <path key={"cty" + i} d={pathGen(f)} fill="none" stroke="rgba(18,28,22,0.5)" strokeWidth={0.9} strokeLinejoin="round" />
                ))}
              </g>
            )}
            {/* Book-grid highlight — our customers' grids get an unmistakable
                gold ring drawn above everything but labels. */}
            {showBook && shapes.filter((s) => s.row.hasBook).map((s) => (
              <polygon key={"bk" + s.grid_id} points={s.pts} fill="none"
                stroke="#ffc21f" strokeWidth={2.4} strokeLinejoin="round"
                style={{ pointerEvents: "none" }} />
            ))}
            {/* Selected-grid ring on top so selection stays visible even on a
                book (gold-ringed) grid. */}
            {selectedGrid && shapes.filter((s) => s.grid_id === selectedGrid).map((s) => (
              <polygon key={"sel" + s.grid_id} points={s.pts} fill="none"
                stroke="#10243a" strokeWidth={2.6} strokeLinejoin="round"
                style={{ pointerEvents: "none" }} />
            ))}
            {(() => {
              // A grid number that would print on top of a county name is skipped
              // while county names are on: the two collided all over the map
              // ("SCHOHARIE·ALBANY", "QUAC·ATTARAUGUS"). The county name is the
              // orientation layer; the grid's number is still in its tooltip,
              // the table and the selection. The box is the county label's own
              // footprint at its 8-unit font (~0.62 em per character).
              const boxes = [];
              if (showCounties && pathGen && stateGeo) {
                for (const f of stateGeo.features) {
                  const c = pathGen.centroid(f);
                  if (!c || isNaN(c[0])) continue;
                  boxes.push([c[0], c[1], String(f.properties.name || "").length * 8 * 0.62 / 2 + 3, 8]);
                }
              }
              const underCounty = (x, y) => boxes.some(([cx, cy, hw, hh]) => Math.abs(x - cx) < hw && Math.abs(y - cy) < hh);
              return shapes.map((s) => {
              const lbl = labelFor(s.row);
              if (lbl === "" || lbl == null) return null;
              if (underCounty(s.cx, s.cy)) return null;
              return (
                <text key={"l" + s.grid_id} x={s.cx} y={s.cy} textAnchor="middle" dominantBaseline="central"
                  fontSize={7} fontWeight={700} fill="#16201a"
                  style={{ paintOrder: "stroke", stroke: "#ffffff", strokeWidth: 1.4, pointerEvents: "none" }}>{lbl}</text>
              );
              });
            })()}
            {/* County name labels — the "where's my farm" orientation layer.
                Drawn above the grid wash with a white halo so they stay legible
                over any heat color. */}
            {showCounties && pathGen && stateGeo && stateGeo.features.map((f, i) => {
              const c = pathGen.centroid(f);
              if (!c || isNaN(c[0])) return null;
              return (
                <text key={"cn" + i} x={c[0]} y={c[1]} textAnchor="middle" dominantBaseline="central"
                  fontSize={8} fontWeight={700} fill="#2a3a30"
                  style={{ paintOrder: "stroke", stroke: "#ffffff", strokeWidth: 2.2, pointerEvents: "none", textTransform: "uppercase", letterSpacing: 0.2 }}>
                  {f.properties.name}
                </text>
              );
            })}
          </svg>
          {/* map display controls — opacity + county labels */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#3a5a3a", fontWeight: 600 }}>
              Grid opacity
              <input type="range" min={0.15} max={0.9} step={0.05} value={gridOpacity} onChange={(e) => setGridOpacity(Number(e.target.value))} style={{ width: 130, accentColor: "#3a6e8a", cursor: "pointer" }} />
              <span style={{ width: 34, textAlign: "right", color: "#5a7258" }}>{Math.round(gridOpacity * 100)}%</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#3a5a3a", fontWeight: 600, cursor: "pointer" }}>
              <input type="checkbox" checked={showCounties} onChange={(e) => setShowCounties(e.target.checked)} style={{ cursor: "pointer" }} />
              County names
            </label>
          </div>
          {/* legend */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, fontSize: 11, color: "#5a7258", alignItems: "center" }}>
            {effMetric === "trigger" && soFarMode && (<>
              {PRF_TODATE_LEGEND.map(([c, l], i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 12, background: c, border: "1px solid #bbb", borderRadius: 2 }} />{l}</span>
              ))}
              <span style={{ color: "#677167" }}>rain so far vs normal for the same days</span>
            </>)}
            {effMetric === "trigger" && !soFarMode && (<>
              {[["#7e1212", "pays ≥28%"], ["#cc3a1e", "15–28%"], ["#e3713c", "5–15%"], ["#f0913a", "<5%"], ["#cfe6c0", "just safe"], ["#3f8f3f", "safe"]].map(([c, l], i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 12, background: c, border: "1px solid #bbb", borderRadius: 2 }} />{l}</span>
              ))}
              <span style={{ color: "#677167" }}>red = share of liability paid @ {coverage}%</span>
            </>)}
            {effMetric === "acres" && <span>Darker green = more insured acres.</span>}
            {showBook && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 12, height: 12, background: "transparent", border: "2px solid #ffc21f", borderRadius: 2 }} />
                = grids with {portal ? "your PRF" : "our customers"}.
              </span>
            )}
            {effMetric !== "acres" && !intervalProgress.complete && (
              <span style={{ color: "#7a8a78", fontStyle: "italic" }}>
                · Interval {Math.round(intervalProgress.elapsed * 100)}% done. No trigger call until it closes.
              </span>
            )}
          </div>
        </div>

        {/* ── Detail sheet (selected grid) ── */}
        {selRow && (
          <div style={{ flex: "1 1 280px", minWidth: 260, maxWidth: isMobile ? "100%" : 360, background: "#fff", border: "1px solid #d6e0d6", borderRadius: 10, padding: 16, position: isMobile ? "static" : "sticky", top: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#1a2e1a" }}>Grid #{selRow.grid_id}</div>
                <div style={{ fontSize: 12, color: "#60735f" }}>CY{cropYear}{selRow.county ? ` · ${selRow.county} County` : ""}</div>
              </div>
              <button onClick={() => setSelectedGrid(null)} style={{ border: "none", background: "#eef3ef", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: 16, color: "#5a7258" }}>×</button>
            </div>

            {/* Interval stepper — cycle intervals for this grid */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 12 }}>
              <button onClick={() => stepInterval(-1)} title="Previous interval" style={{ border: "1px solid #d6e0d6", background: "#f4f8f4", borderRadius: 7, width: 32, height: 32, cursor: "pointer", fontSize: 14, color: "#3a5a3a" }}>◀</button>
              <div style={{ flex: 1, textAlign: "center" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1a2e1a" }}>{PRF_INTERVALS.find((i) => i.code === interval)?.label}</div>
                <div style={{ fontSize: 10, color: selRow.isFinal ? "#2f6e2f" : "#8a6510", fontWeight: 700 }}>
                  {selRow.index == null && selRow.toDateIdx == null ? "no data" : selRow.isFinal ? "RMA FINAL" : selRow.toDateIdx != null ? `SO FAR · ${Math.round(intervalProgress.elapsed * 100)}% done` : "EST AT CLOSE"}
                </div>
              </div>
              <button onClick={() => stepInterval(1)} title="Next interval" style={{ border: "1px solid #d6e0d6", background: "#f4f8f4", borderRadius: 7, width: 32, height: 32, cursor: "pointer", fontSize: 14, color: "#3a5a3a" }}>▶</button>
            </div>

            {/* Headline tiles: index + received-vs-normal with the per-interval $ threshold in inches */}
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <div style={{ flex: 1, textAlign: "center", padding: "10px 6px", background: "#f4f8f4", borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: "#60735f" }}>{selRow.toDateIdx != null ? "% of normal so far" : "Rainfall index"}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: selRow.toDateIdx != null ? "#1a2e1a" : selRow.triggers ? "#a03028" : "#2f6e2f" }}>{selRow.index != null ? Math.round(selRow.index) + "%" : selRow.toDateIdx != null ? Math.round(selRow.toDateIdx) + "%" : "—"}</div>
                <div style={{ fontSize: 10, color: "#677167" }}>{selRow.toDateIdx != null ? "same days · 100% = normal" : "100% = normal"}</div>
              </div>
              <div style={{ flex: 1, textAlign: "center", padding: "10px 6px", background: "#f4f8f4", borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: "#60735f" }}>{intervalProgress.complete || selRow.isFinal ? "Received" : "Received so far"}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1a2e1a" }}>{selRow.rain != null ? selRow.rain.toFixed(2) + '"' : "—"}</div>
                <div style={{ fontSize: 10, color: "#60735f" }}>{selRow.toDateIdx != null
                  ? <>normal so far {selRow.normalSoFar != null ? selRow.normalSoFar.toFixed(2) + '"' : "—"}</>
                  : <>normal {selRow.normal != null ? selRow.normal.toFixed(2) + '"' : "—"}</>}</div>
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, textAlign: "center", color: selRow.triggers ? "#a03028" : "#5a7258", fontWeight: 600 }}>
              {selRow.index == null
                ? (selRow.toDateIdx != null ? "Interval still open. Trigger and payment show once it closes." : "No index published yet")
                : (() => {
                const payPct = Math.round((coverage - selRow.index) / coverage * 100);
                if (selRow.triggers) return `▼ Below ${coverage}% — ${selRow.isFinal ? "pays" : "est. at close"} ${payPct}% of liability`;
                return `▲ Above the ${coverage}% line — no payout`;
              })()}
              {selRow.normal != null && <span style={{ display: "block", fontWeight: 500, color: "#677167", fontSize: 11, marginTop: 2 }}>{coverage}% trigger = {(coverage / 100 * selRow.normal).toFixed(2)}" for this interval</span>}
            </div>

            {/* Coverage $ + payout — driven by RMA County Base Value × productivity
                × coverage level. Protection/ac = CBV × productivity × coverage.
                Settled (RMA final) years show what it actually PAID. */}
            {(selRow.index != null || selRow.toDateIdx != null) && (() => {
              // Open interval: coverage and premium are policy facts and stay;
              // the payout and loss ratio wait for the close.
              const hasIdx = selRow.index != null;
              const pf = hasIdx ? Math.max(0, (coverage - selRow.index) / coverage) : 0; // share of liability paid
              const gridCbv = cbvByGrid[`${selectedGrid}|${cropYear}`]?.[interval]?.hay || 0;
              const protAc = gridCbv * productivity * (coverage / 100); // dollar protection per acre
              const perAc = pf * protAc;
              const past = selRow.isFinal; // settled RMA final
              const isBook = showBook && selRow.hasBook;
              // CY2026 gross premium rate for this grid|interval|coverage → premium/ac
              // (rate × protection) and producer cost (after subsidy). Loss ratio =
              // indemnity factor ÷ gross rate (protection-independent).
              // Narrowed to the chosen rate year BEFORE anything reads it — the
              // fetch deliberately holds both, and a row from the other year is
              // a different actuarial identity, not a fallback.
              const _rrAll = prfRatesByGrid[selectedGrid] || [];
              const _rr = _rrAll.filter((r) => Number(r.crop_year) === Number(prfRateYear));
              const _yearsHere = [...new Set(_rrAll.map((r) => Number(r.crop_year)).filter(Boolean))].sort();
              const _sel = { intervalCode: interval, coverage: coverage / 100,
                             typeCode: prfUse, practiceCode: prfPractice, countyCode: prfRateCounty };
              const _look = lookupPrfRate(_rr, _sel);
              const rate = _look.rate;
              const grossPremAc = (gridCbv > 0 && rate != null) ? protAc * rate : null;
              const producerAc = grossPremAc != null ? grossPremAc * (1 - prfSubsidy(coverage)) : null;
              const lossRatio = hasIdx && rate != null ? pf / rate : null;
              return (
                <div style={{ marginTop: 10, padding: "10px 12px", background: perAc > 0 ? "#fbeceb" : "#f4f8f4", border: "1px solid " + (perAc > 0 ? "#e0b4b0" : "#e0eae0"), borderRadius: 8 }}>
                  {/* ⚠ THE PREMIUM NEEDS AN IDENTITY, SO THE PAGE ASKS FOR ONE.
                      A grid straddles counties and RMA rates haying and grazing
                      separately (hay carries SIX practice bands for CY2027), so
                      an unpicked selection has no single rate. The options are
                      read out of the rate rows themselves — a type or band RMA
                      stops publishing disappears from the picker on its own. */}
                  {(() => {
                    const opts = prfRateOptions(_rr, { intervalCode: interval, coverage: coverage / 100 });
                    const pracs = prfUse ? prfPracticesFor(_rr, { typeCode: prfUse, intervalCode: interval }) : [];
                    // ⚠ GUARD ON BOTH YEARS, NOT THE SELECTED ONE. Keyed on `_rr`
                    // this row vanishes the moment the chosen year has no rows for
                    // the grid — taking the year picker with it, so there is no way
                    // back to the year that does. `_rrAll` keeps the control alive.
                    if (!_rrAll.length) return null;
                    const btn = (on) => ({ padding: "2px 7px", fontSize: 10, borderRadius: 5, cursor: "pointer",
                      border: "1px solid " + (on ? "#2d6a4a" : "#cfdccf"), background: on ? "#2d6a4a" : "#fff",
                      color: on ? "#fff" : "#3a4a3a" });
                    return (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 7, paddingBottom: 6, borderBottom: "1px solid #e0eae0" }}>
                        <span style={{ fontSize: 10, color: "#60735f", fontWeight: 700 }}>RATE FOR</span>
                        {/* ⚠ CHANGING THE YEAR CLEARS THE NARROWING, because the two
                            years are not the same shape: CY2026 carries a NULL
                            practice_code on every row and CY2027 carries six hay
                            bands. A band picked on 2027 narrows CY2026 to nothing,
                            and a blank premium would read as "RMA published no
                            rate" rather than as a stale selection. */}
                        {_yearsHere.length > 1 && _yearsHere.map((y) => (
                          <button key={"cy" + y} style={btn(Number(prfRateYear) === y)}
                            onClick={() => { setPrfRateYear(y); setPrfUse(null); setPrfPractice(null); setPrfRateCounty(null); }}>
                            CY{y}
                          </button>
                        ))}
                        {opts.types.map((t) => (
                          <button key={t} style={btn(prfUse === t)}
                            onClick={() => { setPrfUse(prfUse === t ? null : t); setPrfPractice(null); }}>
                            {PRF_TYPE_LABEL[t] || t}
                          </button>
                        ))}
                        {pracs.length > 1 && pracs.map((p) => (
                          <button key={p} style={btn(prfPractice === p)}
                            onClick={() => setPrfPractice(prfPractice === p ? null : p)}>band {p}</button>
                        ))}
                        {opts.counties.length > 1 && opts.counties.map((c) => (
                          <button key={c} style={btn(prfRateCounty === c)}
                            onClick={() => setPrfRateCounty(prfRateCounty === c ? null : c)}>co {c}</button>
                        ))}
                        {/* The year is named in the provenance line, not only on a
                            button, so a screenshot of a premium always carries the
                            rate table it came from. */}
                        <span style={{ fontSize: 10.5, color: "#60735f" }}>
                          CY{prfRateYear} rates · {prfRateProvenance(_sel, _look)}
                        </span>
                      </div>
                    );
                  })()}
                  {_rrAll.length > 0 && !_rr.length && (
                    <div style={{ fontSize: 10.5, color: "#8a5a00", background: "#fff8ec", border: "1px solid #e8d9b8", borderRadius: 6, padding: "5px 7px", marginBottom: 7, lineHeight: 1.45 }}>
                      RMA has published no CY{prfRateYear} rates for this grid. Nothing is
                      estimated from the other year — pick a year above that has them.
                    </div>
                  )}
                  {_look.ambiguous && (
                    <div style={{ fontSize: 10.5, color: "#8a5a00", background: "#fff8ec", border: "1px solid #e8d9b8", borderRadius: 6, padding: "5px 7px", marginBottom: 7, lineHeight: 1.45 }}>
                      This grid carries <b>{_look.found}</b> rate rows for {interval} at {coverage}% and they do not agree
                      ({(_look.lo * 100).toFixed(2)}%–{(_look.hi * 100).toFixed(2)}%). Pick {(_look.varying || []).join(" and ")} above —
                      nothing is guessed, so no premium is shown until the rate is identified.
                    </div>
                  )}
                  {/* COVERAGE / ACRE */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 11, color: "#60735f", fontWeight: 700 }}>COVERAGE / ACRE</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: "#1a2e1a" }}>{gridCbv > 0 ? "$" + Math.round(protAc).toLocaleString() + "/ac" : "—"}</span>
                  </div>
                  <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 3, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                    {gridCbv > 0 ? <>RMA base <b>${Math.round(gridCbv)}</b> ×</> : "no RMA base value ·"}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                      <input type="number" value={productivity} min={0.6} max={1.5} step={0.1}
                        onChange={(e) => setProductivity(Math.max(0.6, Math.min(1.5, Number(e.target.value) || 1)))}
                        style={{ width: 46, padding: "1px 4px", fontSize: 11, border: "1px solid #ccd", borderRadius: 4 }} /> productivity
                    </span>
                    × {coverage}% coverage
                  </div>
                  {isBook && (
                    <div style={{ fontSize: 10.5, color: "#677167", marginTop: 2 }}>
                      Our policy here: {fmtD0(selRow.intervalLiab)} on {Math.round(selRow.intervalValAcres).toLocaleString()} value-allocated ac{cropYear < new Date().getFullYear() ? ` (CY${cropYear})` : ""}
                    </div>
                  )}
                  {/* PAYOUT / ACRE — closed intervals only */}
                  {hasIdx && (<>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, paddingTop: 8, borderTop: "1px solid #eed9d6" }}>
                    <span style={{ fontSize: 11, color: "#60735f", fontWeight: 700 }}>{past ? "FINAL / ACRE" : "EST AT CLOSE / AC"}</span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: perAc > 0 ? "#a03028" : "#5a7258" }}>{gridCbv > 0 ? "$" + perAc.toFixed(2) + "/ac" : "—"}</span>
                  </div>
                  <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 2 }}>
                    {Math.round(pf * 100)}% of coverage {past ? "pays (RMA final)" : "est. at close"} @ {coverage}%
                  </div>
                  </>)}
                  {/* PREMIUM / ACRE — the selected rate year, producer-paid (after subsidy) */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, paddingTop: 8, borderTop: "1px solid #eed9d6" }}>
                    <span style={{ fontSize: 11, color: "#60735f", fontWeight: 700 }}>PREMIUM / ACRE <span style={{ fontWeight: 500 }}>(CY{prfRateYear})</span></span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: "#1a2e1a" }}>{producerAc != null ? "$" + producerAc.toFixed(2) + "/ac" : "—"}</span>
                  </div>
                  <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 2 }}>
                    {grossPremAc != null
                      ? <>gross <b>${grossPremAc.toFixed(2)}</b> · rate {(rate * 100).toFixed(2)}% · {Math.round(prfSubsidy(coverage) * 100)}% subsidized</>
                      : (rate == null ? (interval === "DECJAN" ? "RMA doesn't offer Dec–Jan" : `no ${prfRateYear} rate for this interval`) : "no RMA base value")}
                  </div>
                  {/* LOSS RATIO — indemnity factor ÷ gross premium rate (at the selected year's rates) */}
                  {lossRatio != null && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, paddingTop: 8, borderTop: "1px solid #eed9d6" }}>
                      <span style={{ fontSize: 11, color: "#60735f", fontWeight: 700 }}>LOSS RATIO <span style={{ fontWeight: 500 }}>{past ? "(RMA final)" : "(est.)"}</span></span>
                      <span style={{ fontSize: 18, fontWeight: 800, color: lossRatio >= 1 ? "#a03028" : "#2f6e2f" }}>{lossRatio.toFixed(2)}×</span>
                    </div>
                  )}
                  {lossRatio != null && (
                    <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 2 }}>
                      indemnity ÷ total premium{producerAc != null && past ? <> · net to insured <b style={{ color: perAc - producerAc >= 0 ? "#2f6e2f" : "#a03028" }}>${(perAc - producerAc).toFixed(2)}/ac</b></> : null}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Per-interval breakdown table — normal vs received vs index, click to cycle */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, color: "#60735f", fontWeight: 700, marginBottom: 4 }}>ALL INTERVALS — NORMAL vs RECEIVED (this grid)</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "#60735f" }}>
                    <th style={{ textAlign: "left", padding: "3px 4px", fontWeight: 600 }}>Interval</th>
                    <th style={{ textAlign: "right", padding: "3px 4px", fontWeight: 600 }}>Normal</th>
                    <th style={{ textAlign: "right", padding: "3px 4px", fontWeight: 600 }}>Got</th>
                    <th style={{ textAlign: "right", padding: "3px 4px", fontWeight: 600 }}>Index</th>
                  </tr>
                </thead>
                <tbody>
                  {PRF_INTERVALS.map((iv) => {
                    const d = selGridStrip[iv.code] || {};
                    const isCur = iv.code === interval;
                    const trig = d.index != null && d.index < coverage;
                    return (
                      <tr key={iv.code} onClick={() => setIntervalCode(iv.code)}
                        style={{ cursor: "pointer", background: isCur ? "#eaf3ea" : "transparent", borderBottom: "1px solid #f2f5f0" }}>
                        <td style={{ padding: "4px", fontWeight: isCur ? 700 : 500, color: "#2d4a2d" }}>{iv.label}{d.inProgress ? " •" : ""}</td>
                        <td style={{ padding: "4px", textAlign: "right", color: "#60735f" }}>{d.normal != null ? d.normal.toFixed(2) + '"' : "—"}</td>
                        <td style={{ padding: "4px", textAlign: "right", color: "#1a2e1a", fontWeight: 600 }}>{d.rain != null ? d.rain.toFixed(2) + '"' : "—"}</td>
                        <td style={{ padding: "4px", textAlign: "right", fontWeight: 700, color: d.index != null ? (trig ? "#a03028" : "#2f6e2f") : d.toDate != null ? "#3a5a7a" : "#ccc" }}>{d.index != null ? Math.round(d.index) + "%" : d.toDate != null ? Math.round(d.toDate) + "%*" : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: 10.5, color: "#677167", marginTop: 3 }}>• still open: “Got” is rain so far and * is % of normal for the same days. Pays only when a closed interval's index is under {coverage}%.</div>
            </div>

            {/* Daily accumulation sparkline — full-year daily filtered to this interval's window */}
            {(() => {
              const all = dailyByGrid[selRow.grid_id];
              if (!all) return <div style={{ marginTop: 14, fontSize: 11, color: "#677167" }}>Loading rainfall accumulation…</div>;
              const iv = PRF_INTERVALS.find((i) => i.code === interval);
              const endY = iv.m1 < iv.m0 ? cropYear + 1 : cropYear;
              const s = new Date(Date.UTC(cropYear, iv.m0, 1)).toISOString().slice(0, 10);
              const e = new Date(Date.UTC(endY, iv.m1 + 1, 0)).toISOString().slice(0, 10);
              const daily = all.filter((d) => d.date >= s && d.date <= e);
              if (!daily.length) return null;
              let cum = 0; const pts = daily.map((d) => { cum += d.mm / 25.4; return { date: d.date, in: cum }; });
              const total = cum, normal = selRow.normal || 0;
              const W = 320, H = 56, maxY = Math.max(total, normal, 0.5);
              const n = pts.length;
              const px = (i) => (n <= 1 ? 0 : (i / (n - 1)) * (W - 4)) + 2;
              const py = (v) => H - 2 - (v / maxY) * (H - 6);
              const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${px(i).toFixed(1)},${py(p.in).toFixed(1)}`).join(" ");
              return (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, color: "#60735f", fontWeight: 700, marginBottom: 4 }}>RAINFALL TO DATE — {total.toFixed(2)}" of {normal ? normal.toFixed(2) + '"' : "—"} normal</div>
                  <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", background: "#f7faf7", borderRadius: 6 }}>
                    {normal > 0 && <line x1={2} y1={py(normal)} x2={W - 2} y2={py(normal)} stroke="#b05050" strokeWidth={1} strokeDasharray="4 3" />}
                    <path d={line} fill="none" stroke="#3a6e8a" strokeWidth={1.8} />
                  </svg>
                </div>
              );
            })()}

            {/* Historical payout frequency for THIS grid+interval over an editable
                year range (from the 2017-2026 RMA final-index backfill). */}
            {gridHistory.length > 0 && (() => {
              const inRange = gridHistory.filter((h) => h.year >= histFrom && h.year <= histTo);
              const n = inRange.length;
              const paid = inRange.filter((h) => h.index < coverage);
              const avgPf = n ? inRange.reduce((s, h) => s + Math.max(0, (coverage - h.index) / coverage), 0) / n : 0;
              const avgWhenPaid = paid.length ? paid.reduce((s, h) => s + (coverage - h.index) / coverage, 0) / paid.length : 0;
              const hot = paid.length / Math.max(1, n) >= 0.4;
              // Loss ratio at the CHOSEN rate year: mean(payout factor) ÷ gross
              // premium rate — "what these final indices would have returned at
              // that year's rates" over the chosen window. It must read the same
              // year the premium above does, or the page shows a loss ratio and a
              // premium computed off two different rate tables.
              const histRate = lookupPrfRate(
                (prfRatesByGrid[selectedGrid] || []).filter((r) => Number(r.crop_year) === Number(prfRateYear)),
                { intervalCode: interval, coverage: coverage / 100,
                  typeCode: prfUse, practiceCode: prfPractice, countyCode: prfRateCounty }).rate;
              const histLR = (histRate != null && n) ? avgPf / histRate : null;
              return (
                <div style={{ marginTop: 14, padding: "10px 12px", background: hot ? "#fbeceb" : "#f4f8f4", border: "1px solid " + (hot ? "#e0b4b0" : "#e0eae0"), borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: "#60735f", fontWeight: 700 }}>HISTORICAL — {PRF_INTERVALS.find((i) => i.code === interval)?.label} @ {coverage}%</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: "#5a7258" }}>
                      <input type="number" value={histFrom} min={2017} max={histTo} onChange={(e) => setHistFrom(Math.max(2017, Math.min(histTo, Number(e.target.value) || 2017)))}
                        style={{ width: 48, padding: "1px 3px", fontSize: 11, border: "1px solid #ccd", borderRadius: 4 }} />–
                      <input type="number" value={histTo} min={histFrom} max={new Date().getFullYear()} onChange={(e) => setHistTo(Math.max(histFrom, Number(e.target.value) || histFrom))}
                        style={{ width: 48, padding: "1px 3px", fontSize: 11, border: "1px solid #ccd", borderRadius: 4 }} />
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: hot ? "#a03028" : "#2d4a2d" }}>
                    Paid in <b>{paid.length} of {n}</b> years{paid.length ? <> · avg <b>{Math.round(avgWhenPaid * 100)}%</b> of liability when it paid</> : null}
                  </div>
                  {histLR != null && (
                    <div style={{ fontSize: 12, color: "#2d4a2d", marginTop: 3 }}>
                      Loss ratio at {prfRateYear} rates: <b style={{ color: histLR >= 1 ? "#a03028" : "#2f6e2f" }}>{histLR.toFixed(2)}×</b> <span style={{ color: "#677167", fontWeight: 500 }}>(indemnity ÷ premium over {n} yr{n === 1 ? "" : "s"})</span>
                    </div>
                  )}
                  {/* per-year mini bars: red = paid (depth = payout), green = safe */}
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 34, marginTop: 6 }}>
                    {inRange.map((h) => {
                      const trig = h.index < coverage;
                      return (
                        <div key={h.year} title={`${h.year}: ${Math.round(h.index)}%`} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                          <div style={{ height: `${Math.max(8, Math.min(100, h.index / 2))}%`, background: trig ? prfTriggerColor(h.index, coverage) : "#9fc99a", borderRadius: "2px 2px 0 0" }} />
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 2, marginTop: 1 }}>
                    {inRange.map((h) => <div key={h.year} style={{ flex: 1, fontSize: 7, textAlign: "center", color: "#677167" }}>{String(h.year).slice(2)}</div>)}
                  </div>
                  <div style={{ fontSize: 10.5, color: "#677167", marginTop: 3 }}>
                    RMA final indices, this grid. {avgPf > 0 ? `Avg ${Math.round(avgPf * 100)}% payout across all ${n} yrs.` : ""}
                    {lossRatioByCounty[selRow.county] ? ` ${selRow.county} market loss ratio CY${lossRatioByCounty[selRow.county].year}: ${lossRatioByCounty[selRow.county].ratio.toFixed(2)}×.` : ""}
                  </div>
                </div>
              );
            })()}

            {/* Book in this grid — the agency's, or in portal the customer's own */}
            {showBook && selRow.hasBook && (() => {
              const bk = bookByGrid[selRow.grid_id];
              return (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #eef0ec" }}>
                  <div style={{ fontSize: 11, color: "#60735f", fontWeight: 700, marginBottom: 6 }}>{portal ? "YOUR PRF IN THIS GRID" : "OUR BOOK IN THIS GRID"}</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: "#1a2e1a" }}><b>{bk.policies}</b> policies</span>
                    <span style={{ fontSize: 12, color: "#1a2e1a" }}><b>{Math.round(bk.acres).toLocaleString()}</b> ac</span>
                    <span style={{ fontSize: 12, color: "#1a2e1a" }}><b>{fmtD0(bk.liab)}</b> liability/yr</span>
                  </div>
                  {bk.customers.slice().sort((a, b) => b.acres - a.acres).map((c, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0", borderBottom: "1px solid #f4f6f2" }}>
                      <span style={{ color: "#2d4a2d" }}>{c.name}</span>
                      <span style={{ color: "#60735f" }}>{Math.round(c.acres).toLocaleString()} ac · {fmtD0(c.liab)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* ── GRID EXPLORER (PRF Advisor only) ──
          The published behaviour of whatever grid is selected, full width because
          a 77-year table does not fit the 360px detail sheet. Rendered from the
          SAME builder the policy view uses, so the explorer and a customer's
          Interval History cannot disagree about the same grid. */}
      {explorer && selectedGrid && !fullscreen && (
        <PRFGridExplorerPanel grid={selectedGrid} cropYear={cropYear} isMobile={isMobile}
                              county={selRow?.county || null} onStartQuote={onStartQuote} />
      )}

      {/* ── Sortable table (hidden in full screen — map + tap-to-open detail only) ──
          Hidden on the Grid Explorer too, and that is a correction rather than a
          preference: the table's job is to rank OUR BOOK's grids, and with the
          book overlay off it stopped filtering to them — dumping all 285 grids
          in the state under a panel about one. The map is the finder here and
          Book Review is where the ranked list belongs. */}
      {!fullscreen && !explorer && (
      <>
      {showBook && (
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 16, fontSize: 12.5, color: "#3a5a3a", cursor: "pointer", userSelect: "none" }}>
          <input type="checkbox" checked={insuredIvlOnly} onChange={(e) => setInsuredIvlOnly(e.target.checked)} style={{ cursor: "pointer" }} />
          {portal ? "Only my grids for" : "Only grids we insure for"} <b style={{ margin: "0 3px" }}>{PRF_INTERVALS.find((i) => i.code === interval)?.label || interval}</b>
          <span style={{ color: "#677167" }}>· {tableRows.length} grid{tableRows.length === 1 ? "" : "s"}</span>
        </label>
      )}
      <div style={{ marginTop: 8, overflowX: "auto", border: "1px solid #e0eae0", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 520 }}>
          <thead style={{ background: "#f4f8f4" }}>
            <tr>
              {th("grid", "Grid", "left")}
              <th style={{ padding: "8px 10px", textAlign: "left", color: "#3a5a3a", fontWeight: 700, borderBottom: "2px solid #d6e0d6" }}>County</th>
              {th("index", "Index %", "right")}
              {th("rain", "Rain", "right")}
              <th style={{ padding: "8px 10px", textAlign: "center", color: "#3a5a3a", fontWeight: 700, borderBottom: "2px solid #d6e0d6" }}>Trigger</th>
              {showBook && th("acres", portal ? "Your acres" : "Our acres", "right")}
              {showBook && th("liab", "Liability", "right")}
              {showBook && !portal && th("policies", "Pols", "right")}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((r) => {
              const canExpand = r.hasBook;
              const isExpanded = expandedGrid === r.grid_id;
              const colSpan = 5 + (showBook ? 2 : 0) + (showBook && !portal ? 1 : 0);
              return (
              <React.Fragment key={r.grid_id}>
              <tr onClick={() => { setSelectedGrid(r.grid_id); if (canExpand) setExpandedGrid((g) => g === r.grid_id ? null : r.grid_id); }}
                title={canExpand ? (isExpanded ? "Hide farms" : "Show farms + potential payment") : undefined}
                style={{ cursor: "pointer", background: isExpanded ? "#eef4ec" : selectedGrid === r.grid_id ? "#eaf3ea" : (showBook && r.hasBook ? "#fbfdf9" : "#fff") }}>
                <td style={{ padding: "7px 10px", fontWeight: 600, color: "#1a2e1a", whiteSpace: "nowrap" }}>
                  {canExpand && (
                    <span style={{ color: "#60735f", fontSize: 11, marginRight: 6, fontWeight: 700 }}>{isExpanded ? "▾" : "▸"}</span>
                  )}
                  #{r.grid_id}{showBook && r.hasBook ? " ●" : ""}
                </td>
                <td style={{ padding: "7px 10px", color: "#5a7258" }}>{r.county || "—"}</td>
                <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: r.index != null ? (r.triggers ? "#a03028" : "#2f6e2f") : r.toDateIdx != null ? "#3a5a7a" : "#bbb" }}>{r.index != null ? Math.round(r.index) + "%" : r.toDateIdx != null ? <>{Math.round(r.toDateIdx)}% <span style={{ fontSize: 10, fontWeight: 600 }}>so far</span></> : "—"}</td>
                <td style={{ padding: "7px 10px", textAlign: "right", color: "#5a7258" }}>{r.rain != null ? r.rain.toFixed(2) + '"' : "—"}</td>
                <td style={{ padding: "7px 10px", textAlign: "center" }}>{r.index == null ? (r.toDateIdx != null ? <span style={{ color: "#677167", fontSize: 11 }}>open</span> : "") : r.triggers ? <span style={{ color: "#a03028", fontWeight: 700 }}>▼ pays</span> : <span style={{ color: "#677167" }}>—</span>}</td>
                {showBook && <td style={{ padding: "7px 10px", textAlign: "right", color: r.acres > 0 ? "#1a2e1a" : "#ccc" }}>{r.acres > 0 ? Math.round(r.acres).toLocaleString() : "—"}</td>}
                {showBook && <td style={{ padding: "7px 10px", textAlign: "right", color: r.liab > 0 ? "#1a2e1a" : "#ccc" }}>{r.liab > 0 ? fmtD0(r.liab) : "—"}</td>}
                {showBook && !portal && <td style={{ padding: "7px 10px", textAlign: "right", color: r.policies > 0 ? "#5a7258" : "#ccc" }}>{r.policies || "—"}</td>}
              </tr>
              {isExpanded && (() => {
                const bk = bookByGrid[r.grid_id];
                const ivLabel = PRF_INTERVALS.find((i) => i.code === interval)?.label || interval;
                // Per-farm potential payout for THIS interval, at each farm's own bought
                // coverage: indemnity = base × max(0, cov − index). base is coverage-
                // independent protection (liability ÷ cov), so this is the true payout,
                // not a slider what-if. A farm's coverage can exceed the slider trigger line.
                const cust = bk?.customers || [];
                const farms = cust
                  .map((c) => {
                    const bi = c.byInterval?.[interval];
                    if (!bi) return null;
                    const covDec = Number(c.cov) || 0.9;
                    const pay = r.index == null ? null : Math.max(0, covDec - r.index / 100) * (bi.base || 0);
                    return { name: c.name, acres: bi.acres || 0, liab: bi.liab || 0, cov: covDec, pay };
                  })
                  .filter(Boolean)
                  .sort((a, b) => (b.pay || 0) - (a.pay || 0) || b.liab - a.liab);
                const totalPay = farms.reduce((s, fm) => s + (fm.pay || 0), 0);
                // Farms that insure this grid but NOT the selected interval — list the
                // intervals they DID pick so the empty state is informative, not a dead end.
                const ivOrder = (code) => PRF_INTERVALS.findIndex((iv) => iv.code === code);
                const otherFarms = cust
                  .map((c) => {
                    if (c.byInterval?.[interval]) return null; // already in the payment table
                    const codes = Object.keys(c.byInterval || {}).sort((a, b) => ivOrder(a) - ivOrder(b));
                    return { name: c.name, labels: codes.map((code) => PRF_INTERVALS.find((iv) => iv.code === code)?.label || code) };
                  })
                  .filter(Boolean);
                return (
                  <tr>
                    <td colSpan={colSpan} style={{ padding: 0, background: "#f7faf6", borderBottom: "1px solid #e0eae0" }}>
                      <div style={{ padding: "10px 14px 12px" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#60735f", marginBottom: 6 }}>
                          FARMS IN #{r.grid_id} · {ivLabel} · {r.index != null ? `index ${Math.round(r.index)}%` : r.toDateIdx != null ? `${Math.round(r.toDateIdx)}% of normal so far` : "index —"}
                        </div>
                        {farms.length > 0 && (
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: "#60735f" }}>
                                <th style={{ textAlign: "left", fontWeight: 600, padding: "2px 8px 4px 0" }}>Farm</th>
                                <th style={{ textAlign: "right", fontWeight: 600, padding: "2px 8px 4px" }}>Acres</th>
                                <th style={{ textAlign: "right", fontWeight: 600, padding: "2px 8px 4px" }}>Liability</th>
                                <th style={{ textAlign: "right", fontWeight: 600, padding: "2px 8px 4px" }}>Cov</th>
                                <th style={{ textAlign: "right", fontWeight: 600, padding: "2px 0 4px 8px" }}>{r.isFinal ? "Final" : "Est. at close"}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {farms.map((fm, i) => (
                                <tr key={i} style={{ borderTop: "1px solid #eef2ec" }}>
                                  <td style={{ padding: "4px 8px 4px 0", color: "#2d4a2d" }}>{fm.name}</td>
                                  <td style={{ padding: "4px 8px", textAlign: "right", color: "#5a7258" }}>{Math.round(fm.acres).toLocaleString()}</td>
                                  <td style={{ padding: "4px 8px", textAlign: "right", color: "#5a7258" }}>{fmtD0(fm.liab)}</td>
                                  <td style={{ padding: "4px 8px", textAlign: "right", color: "#5a7258" }}>{Math.round(fm.cov * 100)}%</td>
                                  <td style={{ padding: "4px 0 4px 8px", textAlign: "right", fontWeight: 700, color: fm.pay > 0 ? "#a03028" : "#677167" }}>{fm.pay > 0 ? fmtD0(fm.pay) : "—"}</td>
                                </tr>
                              ))}
                              {farms.length > 1 && (
                                <tr style={{ borderTop: "2px solid #d6e0d6" }}>
                                  <td style={{ padding: "4px 8px 2px 0", fontWeight: 700, color: "#1a2e1a" }}>Total</td>
                                  <td colSpan={3} />
                                  <td style={{ padding: "4px 0 2px 8px", textAlign: "right", fontWeight: 700, color: totalPay > 0 ? "#a03028" : "#677167" }}>{totalPay > 0 ? fmtD0(totalPay) : "—"}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        )}
                        {otherFarms.length > 0 && (
                          <div style={{ fontSize: 12, color: "#5a7258", marginTop: farms.length ? 8 : 0 }}>
                            {farms.length
                              ? <span style={{ color: "#60735f" }}>Also in this grid — no {ivLabel} coverage:</span>
                              : <span style={{ color: "#60735f" }}>Not insured for {ivLabel} here. Covered intervals:</span>}
                            {otherFarms.map((o, i) => (
                              <div key={i} style={{ marginTop: 3 }}>
                                <span style={{ color: "#2d4a2d", fontWeight: 600 }}>{o.name}</span>
                                <span style={{ color: "#60735f" }}> — {o.labels.join(", ") || "no intervals"}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {farms.length > 0 && (
                          <div style={{ fontSize: 10.5, color: "#677167", marginTop: 6 }}>
                            {r.index == null
                              ? "Payments show once this interval closes."
                              : <>Indemnity at each farm's own coverage level and the {r.isFinal ? "final" : "estimated"} index — a farm can pay even when the grid's trigger line (slider {coverage}%) doesn't.</>}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })()}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRF GRID PAGE — its own screen, agent and portal alike (moved 2026-08-21).
//
// It used to be a tab in the Markets hub. That hub's contract is market DATA:
// every other tab there reads no farmer data, which is precisely what lets a
// Markets grant be WIDER than the coverage a customer carries. PRFGridMapView
// reads `farmers` and draws the book over the map, so it needed a hand-written
// exception to that rule — and the exception still leaked, putting a PRF tab in
// front of a DRP/LRP-only dairy (Laier). A page of its own retires the
// exception instead of patching around it.
//
// The old PRFTabbedView wrapper carried a ONE-entry tab bar plus a "policies"
// branch nothing could ever select; both are gone. Coverage lists live on the
// farmer card.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// PRF COVERAGE REVIEW — the producer-facing PRF screen
// ─────────────────────────────────────────────────────────────────────
// Five sections over ONE engine: src/prf/prfReview.js. Every number on this
// page — current coverage, every coverage rung, every simulated change, every
// optimizer candidate — is produced by `priceAndReplay`, so two sections cannot
// disagree about the same allocation. The quote engine underneath reconciles to
// RMA's own rating service to the dollar.
//
// ⚠ IT READS FARMER DATA, so it is a scoped surface. Every customer table it
// touches (policies, prf_policies, prf_intervals) carries a farmer-scoped RLS
// policy and is read with the VIEWER's own session — an admin sees the book, a
// portal user sees their own rows, and preview holds the farmer's token. The
// reference tables (prf_index_history, prf_premium_rates, prf_county_base_values,
// prf_interval_limits) are RMA's published actuarials and carry no farmer.
// In portal it is gated on `lobSet.has("prf")` beside the PRF Grid page.
//
// ⚠ "LOW-INDEX YEARS" IS NOT "DROUGHT YEARS". Every severity figure here ranks
// RMA's rainfall index for this grid. No forage outcome was observed — see
// docs/prf-risk-relevance-plan.md — and no label on this screen may imply one.
// ── ONE DEFINITION OF "WHAT RMA PUBLISHES FOR THIS GRID" ───────────────
//
// The renewal loader fetched these six tables inline, per grid, in two loops —
// and the grid picker below now has to fetch the SAME set on demand for a grid
// the policy does not carry. Two copies of a fetch set is how one of them comes
// to select a column the other does not (the `price_election_pct` defect this
// file already records), so it is written once.
//
// `withHistory` is the difference between the two callers and nothing else:
// the POLICY year needs the index history, the settled indices and RMA's
// published normals because the evidence matrix and the loss-ratio replay read
// them; the QUOTE year needs only what prices a renewal. Asking for history on
// the quote year would be four reads nothing renders.
// ── THE POLICY NAMES A COUNTY; THE ACTUARIAL TABLE CODES IT ────────────
//
// Matching them is a lookup between two sourced facts, not a guess, and it
// REFUSES unless exactly one code in the table carries that name — grid 27509
// spans four counties, so a wrong pick is a different county base value.
//
// ⚠ ONE DEFINITION. The quote resolves the county this way and so does the
// review; two copies is how they come to disagree about which county a block
// is in, and the review is what drives the coverage ladder and the loss ratio.
function prfCountyCodeForName(cbvRows, name) {
  const want = String(name || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (!want) return null;
  const hits = new Set();
  for (const r of cbvRows || []) {
    const nm = FIPS_TO_NAME_GLOBAL[String(r.state_code || "") + String(r.county_code || "")];
    if (nm && nm.toUpperCase().replace(/[^A-Z]/g, "") === want) hits.add(String(r.county_code));
  }
  return hits.size === 1 ? [...hits][0] : null;
}
async function prfGridRefs(grid, year, { withHistory }) {
  const g = Number(grid), RY = Number(year);
  if (!Number.isFinite(g) || !Number.isFinite(RY)) return null;
  // Every published value for this grid — BOTH intended uses and every
  // practice. Narrowing to haying here is what mispriced grazing acres.
  // `interval_code` rides along because the renewal quote narrows the county
  // base value PER INTERVAL — on CY2027 a band's intervals can carry different
  // values, and a rule that cannot see the interval cannot notice.
  const [cbvRows, rateRows, limitRows] = await Promise.all([
    sbFetch("prf_county_base_values", {
      select: "grid_id,crop_year,type_code,practice_code,county_code,state_code,interval_code,county_base_value",
      filters: [`grid_id=eq.${g}`, `crop_year=eq.${RY}`], maxPages: 3,
    }).catch(() => []),
    // ⚠ NO maxPages. sbFetch defaults to 50 pages and the policy-year rate
    // fetch this replaced carried no override, so pinning 5 here would have
    // been a TENFOLD NARROWING of a read that already runs to 1,760 rows on a
    // single grid-year. A short read does not error — it drops rate rows, and a
    // coverage level that then finds none prices nothing while looking fine.
    sbFetch("prf_premium_rates", {
      select: "interval_code,coverage_level,base_rate,type_code,county_code,practice_code",
      filters: [`grid_id=eq.${g}`, `crop_year=eq.${RY}`],
    }).catch(() => []),
    sbFetch("prf_interval_limits", {
      select: "grid_id,crop_year,interval_code,min_percent,max_percent",
      filters: [`grid_id=eq.${g}`, `crop_year=eq.${RY}`], limit: 50,
    }).catch(() => []),
  ]);
  if (!withHistory) {
    // A year RMA has not published yet is ABSENT, never an empty quote — the
    // caller offers only the years it can actually price.
    if (!(cbvRows || []).length || !(rateRows || []).length) return null;
    return { cbvRows, rateRows, limitRows: limitRows || [], cropYear: RY };
  }
  // ⚠ THE VINTAGE STOPS BEFORE THE LAST SETTLED YEAR, AND IT IS NOT A GAP IN
  // THE FEED. RMA restates its whole published history against an advancing
  // normal (the mean of 1948..Y-2), so the CY2026 file runs 1948-2024 by
  // construction — 2025 is settled and simply is not in it. `settledRows` are
  // the SETTLED indices RMA paid on, appended by mergeSettledYears under its own
  // rules and NAMED on screen as a different basis. `final_rainfall_inches` is
  // RMA's OWN measured inches for a settled interval and is the only inches
  // figure allowed on screen; populated 2017-2026 only, so earlier years render
  // blank rather than being back-solved from the index.
  const [indexRows, settledRows, normalRows] = await Promise.all([
    sbFetch("prf_index_history", {
      select: "year,interval_code,index_value",
      filters: [`grid_id=eq.${g}`, `source_crop_year=eq.${RY}`],
      order: "year.asc", maxPages: 5,
    }).catch(() => []),
    sbFetch("prf_grid_indices", {
      select: "crop_year,interval_code,final_index_value,final_rainfall_inches",
      filters: [`grid_id=eq.${g}`, "final_index_value=not.is.null"],
      order: "crop_year.asc", maxPages: 3,
    }).catch(() => []),
    // RMA's PUBLISHED normal, in inches. Both halves of the rainfall view come
    // from RMA and are therefore comparable; a climatology normal from any other
    // source would not be, and must never be substituted.
    sbFetch("prf_grid_normals", {
      select: "interval_code,normal_inches,method,methodology",
      filters: [`grid_id=eq.${g}`, `methodology=eq.${prfNormalMethodology(RY)}`], limit: 50,
    }).catch(() => []),
  ]);
  return {
    cbvRows: cbvRows || [], rateRows: rateRows || [], limitRows: limitRows || [],
    indexRows: indexRows || [], settledRows: settledRows || [], normalRows: normalRows || [],
    cropYear: RY, sourceCropYear: RY,
  };
}

// `farmer` (singular) EMBEDS this view inside one operation's coverage card. Kevin,
// 2026-09-07: "what does PRF coverage page do that PRF in My coverage doesnt do? I
// think thats an unneccesary overlap" — the two screens answer different questions
// (what did the rain do, vs what did I buy and what would another level cost) but
// they repeated the whole policy header between them and sat as two separate nav
// items, so the reader had to know which one held which half.
//
// ⚠ EMBEDDING PINS THE FARMER AND IGNORES sessionStorage. `activeFarmerId` falls
// back to a stored `cloverag_prf_farmer`, which is the id of whoever the STANDALONE
// page was last opened on. Handed a one-element array for a different operation that
// id matches nothing, `activeFarmer` resolves null, and the tab renders empty — a
// working policy that looks like missing data. So an embedded view takes its farmer
// as a prop, never from storage, and never WRITES storage either: switching
// operations inside My Coverage must not silently repoint the standalone page.
function PRFReviewView({ farmers, farmer: embeddedFarmer = null, isMobile, mode = "admin" }) {
  const isPortal = mode === "portal";
  const embedded = !!embeddedFarmer;
  const CROP_YEAR = 2026;
  const [farmerId, setFarmerId] = useState(() => {
    try { return sessionStorage.getItem("cloverag_prf_farmer") || null; } catch { return null; }
  });
  const [blockKey, setBlockKey] = useState(() => {
    try { return sessionStorage.getItem("cloverag_prf_block") || null; } catch { return null; }
  });
  const [section, setSection] = useState(() => {
    // The quote is the page. A saved "intervals" from the removed assessment
    // tab would land on nothing, so anything unrecognised falls back here.
    // ⚠ AND A PORTAL USER MAY NOT RESTORE THE QUOTE TAB. The key is written by
    // the agent app in the same browser, and preview-as is an impersonation in
    // that same browser — sessionStorage survives the hard reload that drops
    // `_sbCache`, so an unguarded restore walks a stored "quote" straight into
    // a customer session. Same shape as the per-acre input program.
    const KEYS = mode === "portal"
      ? ["coverage", "history"]
      : ["quote", "coverage", "history", "current"];
    const FALLBACK = mode === "portal" ? "coverage" : "quote";
    try {
      const s = sessionStorage.getItem("cloverag_prf_section");
      return KEYS.includes(s) ? s : FALLBACK;
    } catch { return FALLBACK; }
  });
  useEffect(() => { try { sessionStorage.setItem("cloverag_prf_section", section); } catch {} }, [section]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);          // { policy, blocks, refsByGrid }
  const [simAlloc, setSimAlloc] = useState(null);
  // Why an allocation shortcut refused, when it did. Cleared on the next
  // successful apply so a stale reason cannot outlive the state it described.
  const [allocNote, setAllocNote] = useState(null);
  const [simCov, setSimCov] = useState(null);

  // ── QUOTING A GRID THE POLICY DOES NOT CARRY ────────────────────────
  // Kevin, 2026-09-03: pick a customer, pick a grid — "we also need ability to
  // quote new grids the customer may not have been in last year."
  //
  // The refs are fetched through the SAME `prfGridRefs` the policy loader uses
  // and merged into `data`, so every surface below (the quote, the coverage
  // ladder, the loss-ratio replay, the evidence matrix) runs unchanged on a grid
  // that has no policy behind it. Nothing here is a second pricing path.
  const [ngOpen, setNgOpen] = useState(false);
  const [ngId, setNgId] = useState("");
  const [ngBusy, setNgBusy] = useState(false);
  const [ngErr, setNgErr] = useState(null);
  const [ngGrid, setNgGrid] = useState(null);      // the grid whose refs are loaded
  const [ngDraft, setNgDraft] = useState({ county: "", use: "", acres: "", cov: "", prod: "", family: "", alloc: {} });

  // Every field is EMPTY until a human answers it — see gridBlock's header.
  // `missing` is what the quote gates on; there is no default anywhere in here.
  const ngResult = useMemo(() => {
    if (!ngGrid || !data) return null;
    const cbvRows = data.refsByGrid?.[ngGrid]?.cbvRows || [];
    // Resolved through the SAME rules the renewal quote uses — the county by
    // name, the type from the intended use, and the practices RMA publishes for
    // that pair. Six haying bands collapse to two named practices; grazing
    // publishes one, unnamed, and renders raw.
    const countyCode = prfCountyCodeForName(cbvRows, ngDraft.county);
    const typeCode = PRF_RQ.typeForUse(ngDraft.use);
    const intervals = Object.keys(ngDraft.alloc).filter((k) => Number(ngDraft.alloc[k]) > 0);
    // ⚠ NULL MEANS "EVERY TYPE" TO bandFamilies, so the choices are asked for
    // only once BOTH the county and the type are known. Asking earlier offers
    // grazing's band on a haying quote — the defect the band picker already
    // carries a guard for.
    const choices = (countyCode && typeCode && intervals.length)
      ? PRF_RQ.practiceChoices(cbvRows, { countyCode, typeCode }, intervals) : [];
    const r = PRF_RQ.gridBlock({
      grid: ngGrid, county: ngDraft.county, intendedUse: ngDraft.use,
      acres: ngDraft.acres, coverage: ngDraft.cov,
      productivity: ngDraft.prod, allocation: ngDraft.alloc,
      family: ngDraft.family, bandsOffered: choices.length > 0,
    });
    return { ...r, choices, countyCode, typeCode };
  }, [ngGrid, ngDraft, data]);

  const loadNewGrid = useCallback(async () => {
    const g = Number(String(ngId).trim());
    if (!Number.isFinite(g) || g <= 0) { setNgErr("Enter a PRF grid id."); return; }
    if (!data) return;
    setNgBusy(true); setNgErr(null);
    try {
      const RY = Number(data.policy?.crop_year) || null;
      const QY = Number(data.quoteYear) || null;
      const [refs, qrefs] = await Promise.all([
        RY ? prfGridRefs(g, RY, { withHistory: true }) : null,
        QY ? prfGridRefs(g, QY, { withHistory: false }) : null,
      ]);
      // A grid RMA publishes nothing for is REFUSED and named, never carried
      // forward as an empty quote — the same rule the year picker follows.
      if (!refs || !refs.cbvRows.length) {
        setNgErr(`RMA publishes no CY${RY} county base value for grid ${g}. Check the grid id on the PRF Grid map.`);
        setNgBusy(false); return;
      }
      setData((d) => ({
        ...d,
        refsByGrid: { ...d.refsByGrid, [g]: refs },
        quoteRefsByGrid: qrefs ? { ...d.quoteRefsByGrid, [g]: qrefs } : d.quoteRefsByGrid,
      }));
      setNgGrid(g);
      setNgDraft({ county: "", use: "", acres: "", cov: "", prod: "", family: "", alloc: {} });
    } catch (e) { setNgErr(e.message || String(e)); }
    finally { setNgBusy(false); }
  }, [ngId, data]);

  // Select the new grid the moment it becomes priceable, and hand selection
  // back to the policy when it is cleared — so the page is never sitting on a
  // block that is no longer in the list.
  useEffect(() => {
    if (ngResult && ngResult.ready) setBlockKey(ngResult.block.key);
    else if (!ngGrid && data?.blocks?.length) {
      setBlockKey((k) => (String(k || "").startsWith("new:") ? data.blocks[0].key : k));
    }
  }, [ngResult, ngGrid, data]);
  // ── the renewal quote ────────────────────────────────────────────────
  // Every one of these is null until a human moves it, and null means "use last
  // year's". Seeding is a STARTING POINT with a stated provenance, never a
  // silent default — the moment one is typed the page says so, and Reset puts
  // it back to what the policy actually says.
  const [qYear, setQYear] = useState(null);      // null = the policy's own year
  const [qAcres, setQAcres] = useState(null);    // "" is a real state: cleared
  const [qProd, setQProd] = useState(null);
  // BFR year for the renewal: null = the year assumed from the bill (one BFR year
  // on), "none" = not a BFR, 1..10 = the agent's own. Policy-level, so it is not
  // reset when the block changes.
  const [qBfr, setQBfr] = useState(null);
  const [qBand, setQBand] = useState(null);      // practice family — never defaulted
  const [qCounty, setQCounty] = useState(null);
  const [qType, setQType] = useState(null);

  // Farmers that actually carry PRF. In portal `farmers` is already the
  // viewer's own list, so the same filter serves both apps.
  // The newest PRF policy on a farmer that actually carries interval rows, or
  // null. `prfPolicies` is unordered and spans crop years, so the newest is
  // taken explicitly rather than by position.
  const prfPolicyFor = useCallback((f) => {
    const withIvs = (f?.prfPolicies || []).filter((p) => (p.intervals || []).length);
    if (!withIvs.length) return null;
    return withIvs.slice().sort((a, b) => (Number(b.cropYear) || 0) - (Number(a.cropYear) || 0))[0];
  }, []);
  const prfFarmers = useMemo(
    () => (embedded ? [embeddedFarmer] : (farmers || [])).filter((f) => prfPolicyFor(f)),
    [farmers, embedded, embeddedFarmer, prfPolicyFor]);
  // Kevin, 2026-08-24: opening PRF for a customer with a policy should show
  // that policy, not a picker over an empty page. The agent can still switch;
  // the portal only ever has its own.
  // Ordered by the crop year of their PRF, newest first, then by name. Opening
  // on a customer whose PRF is two years stale is a worse first screen than
  // opening on one whose policy is current, and alphabetical order picks the
  // stale one by accident.
  const prfSorted = useMemo(() => prfFarmers.slice().sort((a, b) =>
    (Number(prfPolicyFor(b)?.cropYear) || 0) - (Number(prfPolicyFor(a)?.cropYear) || 0) ||
    (a.name || "").localeCompare(b.name || "")), [prfFarmers, prfPolicyFor]);
  // The picker itself reads better alphabetically, and names the crop year so
  // a customer whose PRF is a year behind is visible as that rather than as a
  // page that failed.
  const prfByName = useMemo(() => prfSorted.slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || "")), [prfSorted]);
  // A deep-link may name a POLICY rather than a farmer — resolve it against the
  // same list the picker renders, so it can only ever select a customer the
  // screen can actually serve.
  const linkedPolicy = (() => { try { return sessionStorage.getItem("cloverag_prf_policy"); } catch { return null; } })();
  const byPolicy = linkedPolicy
    ? prfFarmers.find((f) => (f.prfPolicies || []).some((p) => p.policyNumber === linkedPolicy))
    : null;
  // ⚠ EMBEDDED PINS. A stored id from the standalone page names another operation,
  // and `prfFarmers.find` would return null against a one-element list — a working
  // policy rendering as an empty tab.
  const activeFarmerId = embedded
    ? (prfFarmers[0]?.id || null)
    : (farmerId || byPolicy?.id || prfSorted[0]?.id || null);
  const activeFarmer = prfFarmers.find((f) => f.id === activeFarmerId) || null;

  useEffect(() => {
    if (!activeFarmerId) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null); setData(null);
      try {
        const f = activeFarmer;
        const prfPol = prfPolicyFor(f);
        if (!prfPol) {
          // Three causes, three messages. The picker is built from this same
          // predicate, so "not found" means the farmer array the loader saw is
          // not the one the picker rendered from — a wiring fault, not bad data.
          if (!f) {
            // ⚠ THE COUNT MUST NAME THE LIST THIS VIEW ACTUALLY SEARCHED. Embedded,
            // `farmers` is null by design and the source is the single operation
            // prop — reporting "0 customers" there would send the next reader to
            // look for a loader fault in the wrong app.
            throw new Error("Customer " + String(activeFarmerId).slice(0, 8) +
              " is offered in the list but is not in the loaded book (" +
              (embedded ? "embedded in one operation's coverage card"
                        : (farmers || []).length + " customers") +
              "). This is a wiring fault, not missing data.");
          }
          const pol = f.prfPolicies || [];
          throw new Error(pol.length
            ? pol.length + " PRF polic" + (pol.length === 1 ? "y" : "ies") +
              " loaded for " + (f.name || "this customer") + " but none carries interval rows."
            : "No PRF policy is loaded on " + (f.name || "this customer") + ".");
        }
        const policy = { policy_number: prfPol.policyNumber, crop_year: Number(prfPol.cropYear) || f?.cropYear || CROP_YEAR,
                         county: prfPol.county || null };

        // The engine takes snake_case rows (it is shared with the tools and the
        // checker, which read the database directly), so the in-memory
        // camelCase shape is mapped here rather than in two places.
        const RY0 = Number(policy.crop_year) || CROP_YEAR;
        // field_prf_intervals is the M13 per-CLU PRF record. It states the
        // intended use and the county, which is what makes grazing acres
        // resolvable at all. Reference data about this policy, read with the
        // viewer's own session like everything else here.
        const fpiRows = await sbFetch("field_prf_intervals", {
          select: "policy_number,crop_year,grid_id,interval_code,intended_use,type_code,county_fips,county,protection_per_acre,coverage_level,productivity_factor",
          filters: [`policy_number=eq.${encodeURIComponent(policy.policy_number)}`], maxPages: 3,
        });
        const ivRows = (prfPol.intervals || []).map((iv) => ({
          grid_id: iv.gridId || iv.grid_id, county: iv.county,
          interval_code: iv.intervalCode || iv.interval_code,
          percent_value: iv.percentValue ?? iv.percent_value,
          insured_acres: iv.insuredAcres ?? iv.insured_acres,
          liability: iv.liability, premium: iv.premium,
          productivity_factor: iv.productivityFactor ?? iv.productivity_factor,
          insured_share: iv.insuredShare ?? iv.insured_share ?? null,
        }));
        // Per-interval intended use, straight from the AIP's M13 record, so a
        // block is never a mix of grazing and haying acres — they carry base
        // values about five times apart and have no single price.
        const useByIv = {};
        for (const fr of (fpiRows || [])) {
          if (!fr.intended_use) continue;
          useByIv[`${fr.crop_year}|${Number(fr.grid_id)}|${fr.interval_code}`] = fr.intended_use;
        }
        const useOf = (iv) => useByIv[`${RY0}|${Number(iv.grid_id)}|${iv.interval_code}`] || null;
        const blocks = PRF_REVIEW.prfBlocks(ivRows, prfPol.coverageLevel ?? prfPol.coverage_level, useOf);
        if (!blocks.length) throw new Error("This PRF policy has no interval rows loaded.");

        // Reference data per grid. All four tables are RMA's published
        // actuarials; none carries a farmer.
        const refsByGrid = {};
        const RY = RY0;
        for (const g of [...new Set(blocks.map((b) => b.grid))]) {
          refsByGrid[g] = await prfGridRefs(g, RY, { withHistory: true });
        }

        // ── NEXT YEAR'S ACTUARIALS, for the renewal quote ──────────────
        // RMA publishes rates and county base values BEFORE a crop year runs,
        // which is why a renewal can be priced at all. Loaded separately and
        // NEVER merged with the policy year's: on CY2027 `practice_code` is
        // populated and moves the county base value, so a row from one year
        // cannot stand in for the other. `quoteRefsByGrid` is empty when RMA
        // has published nothing yet, and the Quote tab then offers only the
        // year it can actually price.
        const QY = RY + 1;
        const quoteRefsByGrid = {};
        for (const g of [...new Set(blocks.map((b) => b.grid))]) {
          const qr = await prfGridRefs(g, QY, { withHistory: false });
          if (qr) quoteRefsByGrid[g] = qr;
        }
        if (!cancelled) {
          // ⚠ BEGINNING FARMER AND RANCHER, READ OFF THE BILL (MGR-25-006). The
          // policy's own subsidy ÷ total premium, less the plan rate, names the
          // BFR points it was written with. Every policy-year price on this page
          // uses them, and the renewal assumes the farmer one BFR year on.
          const bfrBilled = PRF_QUOTE_RULES.bfrFromBilled({
            totalPremium: prfPol.totalPremium, subsidy: prfPol.subsidy,
            coverageLevel: prfPol.coverageLevel ?? prfPol.coverage_level });
          setData({ policy, prfPol, blocks, refsByGrid, quoteRefsByGrid, quoteYear: QY,
                    fpiRows: fpiRows || [], ivRows, bfrBilled });
          // Honour a deep-linked block when this policy has it; otherwise the
          // largest, which is what prfBlocks sorts first.
          let want = null;
          try { want = sessionStorage.getItem("cloverag_prf_block"); } catch {}
          setBlockKey(blocks.find((b) => b.key === want)?.key || blocks[0].key);
          setSimAlloc(null); setSimCov(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [activeFarmerId, activeFarmer, prfPolicyFor]);

  // ── THE BLOCK BEING PRICED ─────────────────────────────────────────
  // The policy's own blocks, plus — once every input it needs has been answered
  // — a block for a grid this customer does not currently insure. It is added
  // ONLY when `ready`, because a half-answered block reaches buildBlockReview
  // with a null coverage level and prices nothing; the panel below reports what
  // is still missing instead.
  //
  // ⚠ THE POLICY ROLLUPS BELOW STILL READ `data.blocks`, DELIBERATELY. The
  // whole-policy table and policyOverview state what this grower has bought; a
  // hypothetical grid must never be summed into either.
  const allBlocks = useMemo(
    () => (ngResult && ngResult.ready ? [...(data?.blocks || []), ngResult.block] : (data?.blocks || [])),
    [data, ngResult]);
  const block = allBlocks.find((b) => b.key === blockKey) || allBlocks[0] || null;
  const review = useMemo(() => {
    if (!data || !block) return null;
    const refs = data.refsByGrid[block.grid];
    if (!refs || !refs.cbvRows?.length) return { fatal: "NO_CBV", grid: block.grid };
    const solveIv = (data.ivRows || []).find((r) =>
      Number(r.grid_id) === block.grid && Number(r.insured_acres) === block.acres && Number(r.liability) > 0);
    // What the human picked, resolved through the SAME rules the quote uses —
    // the county by name, and the county base value for the elected practice
    // across the elected intervals. `cbvForElection` refuses where the value is
    // not single, so a block that cannot be priced says so rather than taking
    // whichever row sorted first.
    let newGridRefs = null;
    if (block.is_new_grid) {
      const cc = prfCountyCodeForName(refs.cbvRows, block.county);
      const tc = PRF_RQ.typeForUse(block.intended_use);
      const cv = PRF_RQ.cbvForElection(refs.cbvRows,
        { countyCode: cc, typeCode: tc, family: block.family ?? null }, block.intervals || []);
      newGridRefs = { countyCode: cc, typeCode: tc, cbv: cv.ok ? cv.cbv : null };
    }
    try {
      return PRF_REVIEW.buildBlockReview(block, {
        ...refs,
        cbvIndex: PRF_REVIEW.indexCbv(refs.cbvRows),
        fpiIndex: PRF_REVIEW.indexFieldPrf(data.fpiRows),
        policyNumber: data.policy.policy_number,
        stateCode: data.policy.state || null,
        solveLiability: solveIv ? Number(solveIv.liability) : null,
        solvePercent: solveIv ? Number(solveIv.percent_value) : null,
        bfrBonus: data.bfrBilled?.bonus || 0,
        // ⚠ A GRID WITH NO POLICY BEHIND IT SOLVES NOTHING. resolveCbv works
        // backwards from the AIP's own stored liability, so for a grid this
        // customer does not insure there is none — measured on grid 27509,
        // which spans four counties, where the whole review came back
        // RATE_AMBIGUOUS with no coverage ladder and no loss ratio.
        //
        // These three are what a human picked, and they are supplied ONLY for
        // such a block: a solved value always wins, and a policy block passes
        // undefined here exactly as it did before.
        ...(block.is_new_grid ? newGridRefs : null),
      });
    } catch (e) { return { fatal: "BUILD_FAILED", message: e.message }; }
  }, [data, block]);

  // ── THE RENEWAL ─────────────────────────────────────────────────────
  // BOTH years are priced through the SAME rule (`quoteRenewal`), so the
  // comparison is like-for-like rather than one year off the review engine and
  // the other off a quoting engine. Last year is priced at the structure the
  // policy actually carries; next year at whatever is on screen.
  // ── ONE PRICING PATH, TWO CALLERS ────────────────────────────────────
  // The screen prices the SELECTED block with whatever the agent has typed;
  // the print package prices EVERY block on the policy as written. They must
  // not be two functions — a package that disagrees with the page it was
  // printed from is the failure this repo keeps recording.
  const priceRenewal = useCallback((blk, rev, o = {}) => {
    if (!data || !blk || !rev || rev.fatal) return null;
    const refs = data.refsByGrid[blk.grid];
    const qRefs = data.quoteRefsByGrid?.[blk.grid] || null;
    const policyYear = Number(refs?.cropYear) || null;
    const nextYear = qRefs ? Number(qRefs.cropYear) : null;
    // ⚠ A RENEWAL QUOTER OPENS ON THE RENEWAL. Defaulting to the policy's own
    // year compared the policy with itself and rendered a table of zeros under
    // the heading "CY2026 as written vs CY2026 quoted" — noise on the one view
    // an agent lands on. Next year when RMA has published it; the policy year
    // is one click away and stays the baseline either way.
    const year = o.year == null ? (nextYear ?? policyYear) : Number(o.year);
    const isNext = nextYear != null && year === nextYear;
    const useRefs = isNext ? qRefs : refs;

    // ⚠ THE CBV SOLVE REFUSES TO NAME A COUNTY WHEN THE VALUE MATCHES TWO, AND
    // THAT LEFT THE WHOLE QUOTE COUNTY-BLIND. Grid 27808 straddles GENESEE (037)
    // and ORLEANS (073); both publish $447 non-irrigated, so `resolveCbv`
    // correctly declines to pick — and every narrowing below then ran with no
    // county at all. It was invisible while the grower's own tier agreed across
    // the two, and surfaced the moment the picker showed the OTHER tier, where
    // 037 is $330 and 073 is $333: the irrigated button read "not published
    // here" for a practice RMA publishes in full.
    //
    // The policy states the county by NAME and the actuarial table states it by
    // CODE. Matching them is a lookup between two sourced facts, not a guess —
    // and it refuses unless exactly one code in the table carries that name.
    const countyFromName = prfCountyCodeForName(useRefs?.cbvRows, blk?.county);
    const county = o.county ?? rev.cbv_resolution?.county ?? countyFromName;
    // ⚠ NULL MEANS "EVERY TYPE" TO bandFamilies, so falling through to it offered
    // grazing's 625 on a haying quote — see prfRenewalQuote's band header.
    const type = o.type ?? rev.cbv_resolution?.type_code ?? PRF_RQ.typeForUse(blk?.intended_use) ?? null;
    const bands = PRF_RQ.bandFamilies(useRefs?.cbvRows || [], { countyCode: county, typeCode: type });
    // A year that publishes no band needs none; a year that publishes one needs
    // it CHOSEN. `o.band` is never seeded, because CY2026 carries nothing to seed
    // it from — see prfRenewalQuote's header.
    const band = bands.length ? (o.band == null ? null : Number(o.band)) : null;

    // Seeded from the policy. `o.acres === ""` is a human clearing the box, and
    // that is a refusal, not a fallback to last year's figure.
    // The block as the AIP wrote it: where the review read the share or the
    // productivity factor off the line liabilities, the renewal carries THOSE,
    // not the stored figures that could not reproduce the bill.
    const eff = rev.block || blk;
    const share = Number(eff.share) > 0 ? Number(eff.share) : 1;
    const acres = o.acres == null ? blk.acres : (o.acres === "" ? null : Number(o.acres));
    const prod = o.prod == null ? eff.productivity_factor : (o.prod === "" ? null : Number(o.prod));
    // ⚠ BFR (MGR-25-006). The policy year is priced at the points it was billed
    // at; the next year at one BFR year on (prfQuote.bfrRenewalAssumption) unless
    // the agent set the year.
    const bfrObserved = data.bfrBilled || null;
    const bfrAssumed = PRF_QUOTE_RULES.bfrRenewalAssumption(bfrObserved);
    const bfrPick = o.bfrYear;
    const bfr = !isNext
      ? { bonus: bfrObserved?.bonus || 0, label: bfrObserved?.bonus ? `BFR +${Math.round(bfrObserved.bonus * 100)} pts, as billed` : null, assumed: false, source: "billed" }
      : bfrPick === "none" ? { bonus: 0, label: "Not a BFR (set by agent)", assumed: false, source: "agent" }
      : bfrPick != null ? { year: Number(bfrPick), bonus: PRF_QUOTE_RULES.bfrBonusForYear(Number(bfrPick)),
                            label: `BFR year ${bfrPick} (+${Math.round(PRF_QUOTE_RULES.bfrBonusForYear(Number(bfrPick)) * 100)} pts, set by agent)`, assumed: false, source: "agent" }
      : { ...bfrAssumed, source: bfrAssumed.bonus ? "assumed" : "none" };
    const cov = o.cov ?? blk.coverage_level;
    const alloc = o.alloc || blk.allocation;

    // ── carry last year's SOURCED county base value forward ──────────
    // `rev.cbv_resolution.cbv` is solved from the AIP's own liability, so
    // it is a stated figure. Keeping only the bands consistent with it is
    // inheritance from a source, not a guess — and where several of those
    // bands price identically, that is one product under several codes rather
    // than a question worth stopping the agent for.
    const argsFor = (fam) => ({
      acres, coverage: cov, productivity: prod, allocation: alloc,
      cbvRows: useRefs?.cbvRows || [], rateRows: useRefs?.rateRows || [],
      lookupRate: lookupPrfRate, countyCode: county, typeCode: type, family: fam,
      limits: PRF_REVIEW.limitsFor(useRefs?.limitRows || [], blk.grid, year) || null,
      share, bfrBonus: bfr.bonus,
    });
    const lastCbv = rev.cbv_resolution?.ok ? Number(rev.cbv_resolution.cbv) : null;
    const carried = (band == null && bands.length && lastCbv)
      ? PRF_RQ.familiesMatchingCbv(useRefs?.cbvRows || [], { countyCode: county, typeCode: type },
          Object.keys(alloc).filter((k) => alloc[k] > 0), lastCbv)
      : [];
    const across = (band == null && carried.length) ? PRF_RQ.quoteAcrossBands(carried, argsFor) : null;
    const priced = band != null ? PRF_RQ.quoteRenewal(argsFor(band))
      : across && across.agree ? across.quote
      : across && across.options?.length ? { ok: false, error: "BANDS_DISAGREE" }
      : PRF_RQ.quoteRenewal(argsFor(null));
    // The baseline is the policy AS WRITTEN — its own acres, its own allocation,
    // its own coverage — never the edited figures, or the comparison compares
    // the quote with itself.
    // ── LAST YEAR IS WHAT THE AIP BILLED ─────────────────────────────
    // Not a re-price. `prf_premium_rates` is sparse on the policy year — 55
    // CY2026 rows against 1,320 for CY2027 on grid 27509 — so re-pricing
    // refuses on real blocks and the comparison came out empty on the first
    // printed package. The carrier's own stored liability and gross premium
    // have no such gap and are the better answer to "what did it cost".
    const storedRows = (data.ivRows || []).filter((r) =>
      Number(r.grid_id) === blk.grid && Number(r.insured_acres) === blk.acres);
    const baseline = PRF_RQ.storedBaseline({
      rows: storedRows, coverage: blk.coverage_level, acres: blk.acres,
      cbv: lastCbv, bfrBonus: bfrObserved?.bonus || 0,
    });
    // ── the coverage ladder, at the SAME year as the quote ───────────
    // ⚠ IT WAS PINNED TO THE POLICY YEAR AND THE TWO TABS DISAGREED. With the
    // quote defaulting to the renewal, Coverage printed CY2026's $4,340 at 85%
    // beside a Quote tab saying $4,061 — one farm, one level, two answers,
    // depending which tab you opened. The ladder is priced through the SAME
    // shared `coverageSection` with the quote year's rates and county base
    // value, so the two cannot drift.
    const ladderBand = band != null ? band : (across && across.agree ? carried[0] : null);
    const ladderCbv = priced.ok ? priced.cbv
      : (lastCbv && bands.length === 0 ? lastCbv : null);
    const ladder = (useRefs && ladderCbv) ? (() => {
      try {
        return PRF_REVIEW.coverageSection(
          { ...blk, acres, productivity_factor: prod, share, allocation: alloc, coverage_level: cov },
          { series: rev.history_inputs?.series, years: rev.history_inputs?.years,
            // the FULL classification — SEVERE and NORMAL both, or the ladder's
            // two history columns come back half blank
            severity: rev.history_inputs?.severity || null,
            cbv: ladderCbv, rateRows: useRefs.rateRows || [],
            typeCode: type, countyCode: county,
            // per-interval on a banded year; null where the year publishes none
            practiceCode: ladderBand == null ? null : (iv) => PRF_RQ.practiceFor(ladderBand, iv),
            limits: PRF_REVIEW.limitsFor(useRefs.limitRows || [], blk.grid, year) || null,
            bfrBonus: bfr.bonus,
          });
      } catch (_) { return null; }
    })() : null;

    return {
      // Whether the band list is narrowed to the block's own intended use, or is
      // every use RMA publishes for the county. The heading says which; without it a
      // seven-band list reads as seven bands for HAYING.
      typeScoped: type != null,
      policyYear, nextYear, year, isNext, bands, band, county, type,
      carried, lastCbv, carriedAgree: !!(across && across.agree),
      // Six RMA band codes collapse to the practices that actually move the
      // money — verified per render, never assumed. See practiceChoices.
      choices: PRF_RQ.practiceChoices(useRefs?.cbvRows || [],
        { countyCode: county, typeCode: type },
        Object.keys(alloc).filter((k) => alloc[k] > 0)),
      ladder, ladderCbv,
      acres, prod, cov, alloc, priced, baseline, share, bfr,
      years: [policyYear, nextYear].filter((y) => y != null),
      compare: (baseline.ok && priced.ok) ? PRF_RQ.compareYears(baseline, priced) : null,

      quoteUnavailable: !qRefs,
    };
  }, [data]);


  const renewal = useMemo(() => priceRenewal(block, review, {
    year: qYear, acres: qAcres, prod: qProd, band: qBand,
    county: qCounty, type: qType, cov: simCov, alloc: simAlloc, bfrYear: qBfr,
  }), [priceRenewal, block, review, qYear, qAcres, qProd, qBand, qCounty, qType, simCov, simAlloc, qBfr]);

  usePortalEmptyState("prfreview", isPortal && prfFarmers.length === 0, isPortal && !loading, err);

  // Agent-only, never in portal — gates the optimizer and the methodology
  // drill-downs. A farmer cannot set it, so the unstable engine has no route in.
  const [advanced, setAdvanced] = useState(false);
  // Level 3 view selector: one of MATRIX_VIEWS.
  const [matrixView, setMatrixView] = useState("index");
  const [matrixOpen, setMatrixOpen] = useState(true);
  // ── THE LAYOUT IS THE AGENT'S, AND IT IS WHAT PRINTS ────────────────────
  // Kevin, 2026-10-05: "we may want to limit the intervals to april-september,
  // getting rid of the winter months so it isnt as busy ... So if we make changes
  // to historical data layout, that needs to be what prints."
  //
  // `null` means EVERY interval / EVERY year, which is what the matrix did before
  // this existed — so an agent who never touches the controls sees exactly the
  // table they saw yesterday. An empty ARRAY is a different statement (every
  // interval switched off) and is refused below rather than collapsed into "all",
  // because a table of one column headed Year is a broken screen, not a choice.
  const [mxIvs, setMxIvs] = useState(null);
  const [mxYrs, setMxYrs] = useState(null);
  // Per-agent, because a shared desk is one browser. Same rule as the Home input
  // program: no identity means no store — the layout still works for the session,
  // it simply is not written. An "anon" fallback would put every unidentified
  // session in one bucket, which is the thing being avoided.
  const mxKey = useMemo(() => {
    try { const id = getCurrentUserId && getCurrentUserId(); return id ? "silas.prfMatrix.v1." + id : null; }
    catch (_) { return null; }
  }, []);
  useEffect(() => {
    if (!mxKey) return;
    try {
      const raw = window.localStorage.getItem(mxKey);
      if (!raw) return;
      const v = JSON.parse(raw);
      if (Array.isArray(v.ivs) && v.ivs.length) setMxIvs(v.ivs);
      if (Number.isFinite(Number(v.yrs)) && Number(v.yrs) > 0) setMxYrs(Number(v.yrs));
      if (typeof v.view === "string") setMatrixView(v.view);
    } catch (_) { /* a blocked or cleared store is not an error */ }
  }, [mxKey]);
  useEffect(() => {
    if (!mxKey) return;
    try { window.localStorage.setItem(mxKey, JSON.stringify({ ivs: mxIvs, yrs: mxYrs, view: matrixView })); }
    catch (_) { /* private window, blocked site data — the session still works */ }
  }, [mxKey, mxIvs, mxYrs, matrixView]);

  // ── the interval assessment, and the full evidence matrix behind it ──
  // Both are derived from `history_inputs`, which is the SAME context the rest
  // of this screen prices from — so the heatmap, the matrix and the plan card
  // can never disagree about a year.

  const matrix = useMemo(() => {
    const h = review && !review.fatal ? review.history_inputs : null;
    if (!h) return null;
    const refs = data?.refsByGrid?.[block?.grid];
    // RMA's own inches, keyed interval -> year. Nothing else may fill this.
    const inches = {};
    for (const r of refs?.settledRows || []) {
      const v = Number(r.final_rainfall_inches);
      if (!Number.isFinite(v)) continue;
      (inches[r.interval_code] ||= {})[Number(r.crop_year)] = v;
    }
    const normals = {};
    for (const r of refs?.normalRows || []) {
      const v = Number(r.normal_inches);
      if (Number.isFinite(v)) normals[r.interval_code] = v;
    }
    // ⚠ THE NARROWING HAPPENS HERE, ON THE ONE OBJECT BOTH THE SCREEN AND THE
    // PRINT PACKAGE READ. `buildQuotePackage` closes over this memo, so a layout
    // chosen on screen is the layout that prints without a second code path to
    // keep in step — which is the whole of "the changes arent lost when you go to
    // print". The builder already takes `labels` and `years`, so nothing about
    // the engine changes; it is handed less.
    const mxLabels = (mxIvs && mxIvs.length)
      ? PRF_EM.LAB.filter((l) => mxIvs.includes(l)) : PRF_EM.LAB;
    const mxYearsAll = [...(h.years || [])].sort((a, b) => b - a);
    const mxYears = mxYrs > 0 ? mxYearsAll.slice(0, mxYrs) : mxYearsAll;
    // ⚠ WHETHER THIS LAYOUT IS NARROWED IS A PROPERTY OF THE MATRIX, NOT A SECOND
    // READ OF THE CONTROLS. `buildQuotePackage` used to derive it straight from
    // `mxIvs`/`mxYrs`, which are NOT in that callback's dep array — so an agent
    // could narrow the table, print, and get a sheet whose table was narrowed while
    // its caption said "the complete record", or the reverse. `check-memo-deps`
    // found it. Deriving it HERE means the flag and the table it describes are one
    // value, recomputed together, and no dep array has to remember the connection.
    const narrowed = !!((mxIvs && mxIvs.length && mxIvs.length < PRF_EM.LAB.length) || mxYrs > 0);
    try {
      const built = PRF_EM.buildEvidenceMatrix({
        series: h.series, years: mxYears, labels: mxLabels,
        coverage: block?.coverage_level,
        allocation: h.allocation,
        // Protection per acre is CBV x coverage x productivity — the engine's
        // own definition (`quotePrf`'s protectionPerAcre), derived here rather
        // than divided out of a block total, so a block with no acres reported
        // still renders and the figure cannot drift from what priced the policy.
        protectionPerAcre: Number.isFinite(Number(h.cbv)) && block?.coverage_level > 0
          && Number.isFinite(Number(h.productivity_factor))
          ? Number(h.cbv) * Number(block.coverage_level) * Number(h.productivity_factor)
          : null,
        inches, normals,
      });
      return built ? { ...built, narrowed } : built;
    } catch (_) { return null; }
  }, [review, block, data, mxIvs, mxYrs]);

  // ── LOSS RATIO PER COLUMN, OVER EXACTLY THE YEARS ON SCREEN ──────────────
  // Kevin, 2026-10-05: "at the bottom of each column could you show a histroical
  // loss ratio figure that updates based on which data is being displayed."
  //
  // ⚠ NO NEW DEFINITION. `intervalHistory` already answers this — its
  // `replay_loss_ratio` is mean(payment factor) / (rate x (1 - subsidy)), which the
  // file's own header explains cancels protection and so is comparable across
  // intervals without any allocation. It already takes `years`, so handing it the
  // displayed window is the entire implementation and the ratio moves with the
  // table by construction rather than by a second filter kept in step.
  //
  // The denominator is the PRODUCER premium rate, matching the loss ratios already
  // on this page. RMA's own actuarial ratio uses GROSS premium and is always
  // smaller; mixing the two conventions on one screen is the failure this repo
  // records elsewhere, so it is not done here.
  const matrixLR = useMemo(() => {
    const h = review && !review.fatal ? review.history_inputs : null;
    if (!h || !matrix?.available || !(block?.coverage_level > 0)) return null;
    try {
      const rates = PRF_REVIEW.ratesFor(h.rateRows || [], block.coverage_level,
        { typeCode: h.typeCode, countyCode: h.countyCode, practiceCode: h.practiceCode });
      const subs = PRF_QUOTE_RULES.PRF_SUBSIDY[block.coverage_level];
      const yrs = matrix.rows.map((r) => r.year);
      const ih = PRF_IH.intervalHistory({
        series: h.series || {}, years: yrs, allocation: h.allocation || {},
        coverageLevel: block.coverage_level, rates, subsidyRate: subs,
      });
      const out = {};
      for (const r of ih.intervals || []) out[r.interval] = r.available ? r.replay_loss_ratio : null;
      return out;
    } catch (_) { return null; }
  }, [review, block, matrix]);

  // ── the printable quoting package ───────────────────────────────────
  // EVERY block on the policy, priced through `priceRenewal` — the same path
  // the screen uses — so the sheet cannot disagree with the page. The selected
  // block carries the agent's edits; the others print as written, which is what
  // a package of a whole policy means.
  const buildQuotePackage = useCallback((matrixView) => {
    if (!data?.blocks?.length) return null;
    const out = [];
    for (const b of data.blocks) {
      const refs = data.refsByGrid[b.grid];
      if (!refs?.cbvRows?.length) continue;
      let rev = null;
      try {
        const solveIv = (data.ivRows || []).find((r) =>
          Number(r.grid_id) === b.grid && Number(r.insured_acres) === b.acres && Number(r.liability) > 0);
        rev = PRF_REVIEW.buildBlockReview(b, {
          ...refs,
          cbvIndex: PRF_REVIEW.indexCbv(refs.cbvRows),
          fpiIndex: PRF_REVIEW.indexFieldPrf(data.fpiRows),
          policyNumber: data.policy.policy_number,
          stateCode: data.policy.state || null,
          solveLiability: solveIv ? Number(solveIv.liability) : null,
          solvePercent: solveIv ? Number(solveIv.percent_value) : null,
          bfrBonus: data.bfrBilled?.bonus || 0,
        });
      } catch (_) { continue; }
      if (!rev || rev.fatal) continue;
      const isSel = b.key === block?.key;
      const r = priceRenewal(b, rev, isSel
        ? { year: qYear, acres: qAcres, prod: qProd, band: qBand, county: qCounty, type: qType, cov: simCov, alloc: simAlloc, bfrYear: qBfr }
        : { year: qYear, bfrYear: qBfr });   // the year and BFR are policy-level and follow the page; nothing else does
      if (!r) continue;
      out.push({
        farmer: activeFarmer?.name || "", policyNumber: data.policy.policy_number,
        // ⚠ SAME CODE-AS-NAME BUG, SECOND SURFACE. `priceRenewal` returns `county`
        // as the county CODE it looked the rate up by (see its own `countyCode:
        // county` call sites); `b.county` is the NAME the policy states. Preferring
        // the code put "113" in an exported column headed county, where it is worse
        // than on screen — a spreadsheet outlives the page that explains it.
        grid: b.grid, county: b.county || (r.county ? "county code " + r.county : ""),
        use: b.intended_use || b.use || "",
        year: r.year, baselineYear: r.policyYear,
        coverage: r.cov, productivity: r.prod,
        band: r.band != null ? r.band : (r.carriedAgree ? r.carried[0] : null),
        cbvSource: r.carriedAgree && r.band == null
          ? "county base value carried from last year's, solved from the AIP's liability" : null,
        intervals: PRF_REVIEW.LAB.filter((l) => (r.alloc?.[l] || 0) > 0)
          .map((l) => ({ interval: l, allocation: r.alloc[l] })),
        quote: r.priced?.ok ? r.priced : { ok: false },
        reason: r.priced?.ok ? null : (r.priced?.error || null),
        baseline: r.baseline?.ok ? r.baseline : null,
        compare: r.compare, ladder: r.ladder,
      });
    }
    if (!out.length) return null;
    return {
      farmer: activeFarmer?.name || "", agency: "Clover Ag Risk Management",
      agent: data?.policy?.agent_name || null,
      printedOn: new Date().toLocaleDateString("en-US",
        { year: "numeric", month: "short", day: "numeric" }),
      // ⚠ `includeMatrix` WAS NEVER SET BY ANY CALLER, so the evidence matrix
      // printed NOWHERE — which is exactly what Kevin reported ("We dont have an
      // option to print from the history tab"). It was dropped from the default on
      // 2026-09-05 for a good reason, that 78 rows x 11 intervals is not the front
      // of a quote package. The customisation above is what answers that objection:
      // what prints now is the layout the agent chose, which is typically a handful
      // of intervals over a readable span, and the sheet says so on its face.
      blocks: out, matrix, matrixView, matrixGrid: block?.grid,
      includeMatrix: !!matrix?.available,
      matrixLossRatio: matrixLR,
      // Read off the matrix the sheet is actually printing, never off the controls:
      // `matrix` is in this callback's deps and the controls are not, so a second
      // read of the state here prints a caption that describes a different layout.
      matrixNarrowed: !!(matrix && matrix.narrowed),
      // The loss-ratio page computes from the ladder rows through the SAME
      // helpers this tab renders with, so the sheet and the screen cannot state
      // different loss ratios for one farm. Passed in rather than imported by
      // the printer, which has no other dependency and should keep it that way.
      IH: PRF_IH,
    };
  }, [data, block, matrix, priceRenewal, activeFarmer,
      qYear, qAcres, qProd, qBand, qCounty, qType, simCov, simAlloc, qBfr]);

  const printQuotePackage = useCallback(() => {
    const pkg = buildQuotePackage(matrixView);
    if (!pkg) return;
    const w = window.open("", "_blank");
    if (!w) return;                       // a blocked popup is not an error to swallow loudly
    writePrintWindow(w, PRF_PRINT.renderPrfQuotePackage(pkg));
  }, [buildQuotePackage, matrixView]);

  // ── formatting ────────────────────────────────────────────────────
  const D = (n) => (n == null || !Number.isFinite(n) ? "—" : (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString("en-US"));
  const Dsig = (n) => (n == null || !Number.isFinite(n) ? "—" : (n > 0 ? "+" : n < 0 ? "−" : "") + "$" + Math.abs(Math.round(n)).toLocaleString("en-US"));
  const PC = (n, dp = 0) => (n == null || !Number.isFinite(n) ? "—" : (n * 100).toFixed(dp) + "%");
  const C = { ink: "#1a2e1a", ink2: "#2d4a2d", bg: "#f2f6f2", line: "#d8e2d8", muted: "#6b7d6b",
              gain: "#166f49", cost: "#9c580f", accent: "#2a6499", surface: "#fff" };
  const card = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 8, padding: isMobile ? 12 : 16 };
  const th = { textAlign: "right", padding: "6px 8px", fontSize: 11, letterSpacing: ".05em",
               textTransform: "uppercase", color: C.muted, fontWeight: 600, whiteSpace: "nowrap" };
  const td = { textAlign: "right", padding: "6px 8px", borderTop: `1px solid ${C.line}`, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const th0 = { ...th, textAlign: "left" }, td0 = { ...td, textAlign: "left" };
  const ivColor = (lab) => {
    const i = PRF_REVIEW.LAB.indexOf(lab), t = i / (PRF_REVIEW.LAB.length - 1);
    return `hsl(203 ${28 + 30 * t}% ${82 - 34 * t}%)`;
  };
  const AllocBar = ({ alloc, height = 34 }) => {
    const used = Object.keys(alloc || {}).filter((l) => alloc[l] > 0)
      .sort((a, b) => PRF_REVIEW.LAB.indexOf(a) - PRF_REVIEW.LAB.indexOf(b));
    if (!used.length) return <div style={{ color: C.muted, fontSize: 12 }}>no intervals elected</div>;
    return (
      <div style={{ display: "flex", gap: 2, height, borderRadius: 5, overflow: "hidden" }}>
        {used.map((l) => (
          <div key={l} title={`${l} — ${Math.round(alloc[l] * 100)}% of value`}
               style={{ flex: alloc[l], background: ivColor(l), color: PRF_REVIEW.LAB.indexOf(l) > 5 ? "#fff" : C.ink,
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        minWidth: 0, overflow: "hidden", lineHeight: 1.15 }}>
            <b style={{ fontSize: 10.5, letterSpacing: ".03em" }}>{l}</b>
            <span style={{ fontSize: 10.5 }}>{Math.round(alloc[l] * 100)}%</span>
          </div>
        ))}
      </div>
    );
  };

  // ── shell ─────────────────────────────────────────────────────────
  if (!isPortal && prfFarmers.length === 0) {
    return <div style={{ padding: 24, color: C.muted }}>No customers with a PRF policy are loaded.</div>;
  }
  // ── FOUR JOBS, IN THE ORDER AN AGENT DOES THEM ──────────────────────
  // What is this policy · are the intervals sound · is the level right · what
  // would a change cost. The prescriptive optimizer is NOT one of them: it was
  // measured unstable on 39 of 39 book grids when only the lookback moved, so it
  // is off the primary navigation entirely and unreachable in portal. It
  // survives behind an agent-only toggle as evidence, never as a recommendation.
  // A QUOTER, NOT AN ADVISOR. Kevin, 2026-09-02: the interval assessment was
  // confusing and unnecessary, and the historical matrix is the visual worth
  // keeping. So three tabs, each one job: what a renewal costs, what the other
  // coverage levels cost, and the record. Nothing here ranks or recommends.
  //
  // ⚠ THE QUOTE TAB IS AGENT-ONLY. Kevin, 2026-09-03: "why is PRF quoter
  // showing to portal users? it should not be." It priced a CY2027 renewal off
  // acres, coverage, productivity and an interval election the customer could
  // edit, printed a quoting package, and — since the grid picker landed — would
  // quote any grid in the state. That is the agency's work, not a statement of
  // what this grower bought.
  //
  // Coverage and History stay: those are the customer's own ladder and their
  // own grid's rainfall record, which is what this page is FOR in portal.
  //
  // HIDE THE SURFACE, THEN GUARD THE RENDER — both, because a tab that is
  // merely absent from a list is one stale sessionStorage key away from being
  // reachable. See the render gates below.
  const SECTIONS = [
    ...(isPortal ? [] : [["quote", "Quote"]]),
    ["coverage", "Coverage"],
    ["history", "History"],
  ];

  // The window control's pill style. Declared here, outside CoverageSection, so any
  // other section that grows a window control shares it: two copies of a control
  // style is how the same question comes to look like two different questions.
  const pill = (on) => ({ fontSize: 12, fontWeight: 700, padding: "5px 11px", borderRadius: 999,
      cursor: "pointer", border: "1px solid " + (on ? "#2d4a2d" : C.line),
      background: on ? "#2d4a2d" : "#fff", color: on ? "#fff" : C.ink2 });

  // ⚠ THESE TWO MUST STAY ABOVE THE COMPONENT'S `return` BELOW. They are
  // `const`s, so declaring them after it leaves them permanently uninitialised
  // while the hoisted function components that close over them still render —
  // which throws at paint and passes both check-undef and the build.
  // ── shared vocabulary for an interval's standing ─────────────────────
  // Five states, and the last three are DIFFERENT ANSWERS rather than degrees of
  // the same one: MIDDLE means the history ranked it in the middle, UNSTABLE
  // means the history would not hold still, INSUFFICIENT means there was not
  // enough of it. Collapsing them is what turns "we cannot say" into a verdict.
  const IV_VERDICT = {
    CONSISTENTLY_STRONG: { label: "Consistently strong", bg: "#e6f3ec", fg: "#166f49", bar: "#2e8b62" },
    MIDDLE:              { label: "Middle of the set",   bg: "#eef2f6", fg: "#425466", bar: "#7d95ab" },
    CONSISTENTLY_WEAK:   { label: "Consistently weak",   bg: "#f6efe8", fg: "#9c580f", bar: "#c98b4b" },
    UNSTABLE:            { label: "Unstable",            bg: "#fdf3e0", fg: "#8a6d1f", bar: "#c9a227" },
    INSUFFICIENT:        { label: "Insufficient data",   bg: "#f3f4f5", fg: "#6b7d6b", bar: "#c3cbc3" },
    UNAVAILABLE:         { label: "No history",          bg: "#f3f4f5", fg: "#6b7d6b", bar: "#c3cbc3" },
  };
  const VChip = ({ v, small }) => {
    const d = IV_VERDICT[v] || IV_VERDICT.UNAVAILABLE;
    return (
      <span style={{ background: d.bg, color: d.fg, borderRadius: 5, padding: small ? "1px 6px" : "2px 8px",
                     fontSize: small ? 10.5 : 11.5, fontWeight: 800, letterSpacing: ".02em", whiteSpace: "nowrap" }}>
        {d.label}
      </span>
    );
  };

  return (
    <div style={{ padding: isMobile ? 12 : 20, background: C.bg, minHeight: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 21, color: C.ink }}>
            {isPortal ? "Your PRF Coverage" : "PRF Coverage Review"}
          </h2>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
            Rainfall Index · priced through the RMA-verified quote engine
          </div>
        </div>
        {prfFarmers.length > 1 && (
          <select value={activeFarmerId || ""} onChange={(e) => { setFarmerId(e.target.value || null); setSection("current"); }}
                  style={{ padding: "7px 10px", border: `1px solid ${C.line}`, borderRadius: 6, fontSize: 14, background: "#fff", maxWidth: 320 }}>

            {prfByName.map((f) => {
              const cy = prfPolicyFor(f)?.cropYear;
              return <option key={f.id} value={f.id}>{f.name}{cy ? " · CY" + cy : ""}</option>;
            })}
          </select>
        )}
      </div>

      {!activeFarmerId && <div style={{ ...card, color: C.muted }}>No customer with PRF interval detail is loaded.</div>}
      {loading && <div style={{ ...card, color: C.muted }}>Loading PRF coverage…</div>}
      {err && <div style={{ ...card, borderColor: C.cost, color: C.cost }}>Couldn't load: {err}</div>}

      {/* ── A GRID THIS CUSTOMER DOES NOT INSURE ──────────────────────────
          Rendered ABOVE the review body on purpose: an incomplete draft leaves
          the body showing the policy block (or nothing at all), and a panel
          nested inside it would disappear at exactly the moment somebody is
          filling it in. */}
      {ngOpen && data && !isPortal && (
        <div style={{ ...card, marginBottom: 14, borderColor: "#2d4a2d" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <b style={{ fontSize: 14 }}>Quote another grid</b>
            <span style={{ fontSize: 12.5, color: C.muted }}>
              Not on this policy — nothing here is added to the policy totals below.
            </span>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 10 }}>
            <label style={{ fontSize: 12.5, color: C.ink2 }}>Grid</label>
            <input value={ngId} onChange={(e) => setNgId(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") loadNewGrid(); }}
                   placeholder="e.g. 27808" inputMode="numeric"
                   style={{ width: 110, padding: "5px 8px", border: `1px solid ${C.line}`, borderRadius: 6, fontSize: 13 }} />
            <button onClick={loadNewGrid} disabled={ngBusy}
                    style={{ padding: "5px 12px", border: "1px solid #2d4a2d", borderRadius: 6, fontSize: 12.5,
                             fontWeight: 700, cursor: ngBusy ? "wait" : "pointer", background: "#2d4a2d", color: "#fff" }}>
              {ngBusy ? "Loading…" : "Load grid"}
            </button>
            {ngGrid && (
              <button onClick={() => { setNgGrid(null); setNgId(""); setNgErr(null); }}
                      style={{ padding: "5px 10px", border: `1px solid ${C.line}`, borderRadius: 6,
                               fontSize: 12.5, cursor: "pointer", background: "#fff", color: C.ink2 }}>
                Clear
              </button>
            )}
            {ngErr && <span style={{ fontSize: 12.5, color: C.cost }}>{ngErr}</span>}
          </div>

          {ngGrid && (() => {
            // The counties RMA actually publishes for THIS grid. A grid is a
            // 0.25-degree box and boxes straddle lines, so the county is a
            // choice with a dollar consequence — grid 27808 pays $330 irrigated
            // in GENESEE and $333 in ORLEANS. Named from FIPS so the dropdown
            // reads like the policy does, and the NAME is what is stored, so the
            // quote resolves it through the same name-to-code lookup a policy
            // block uses rather than growing a second path. A code with no name
            // still renders, raw, rather than being dropped.
            const refs = data.refsByGrid?.[ngGrid];
            const counties = PRF_RQ.gridCounties(refs?.cbvRows || []).map((c) => ({
              ...c, name: FIPS_TO_NAME_GLOBAL[c.state_code + c.county_code] || null,
            }));
            const lab = { fontSize: 11.5, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginBottom: 3 };
            const inp = { padding: "5px 8px", border: `1px solid ${C.line}`, borderRadius: 6, fontSize: 13, background: "#fff", width: "100%" };
            const setD = (k, v) => setNgDraft((d) => ({ ...d, [k]: v }));
            const allocPct = Math.round((ngResult?.block.allocation_total || 0) * 100);
            return (
              <>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, minmax(0,1fr))", gap: 10, marginTop: 12 }}>
                  <div>
                    <label style={lab}>County</label>
                    <select value={ngDraft.county} onChange={(e) => setD("county", e.target.value)} style={inp}>
                      <option value="">— select —</option>
                      {counties.map((c) => (
                        <option key={c.state_code + c.county_code} value={c.name || c.county_code}>
                          {c.name || `county ${c.county_code}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={lab}>Intended use</label>
                    <select value={ngDraft.use} onChange={(e) => setD("use", e.target.value)} style={inp}>
                      <option value="">— select —</option>
                      <option value="HAYING">HAYING</option>
                      <option value="GRAZING">GRAZING</option>
                    </select>
                  </div>
                  <div>
                    <label style={lab}>Acres</label>
                    <input value={ngDraft.acres} onChange={(e) => setD("acres", e.target.value)}
                           onFocus={(e) => e.target.select()} inputMode="decimal" style={inp} />
                  </div>
                  <div>
                    <label style={lab}>Coverage</label>
                    <select value={ngDraft.cov} onChange={(e) => setD("cov", e.target.value)} style={inp}>
                      <option value="">— select —</option>
                      {PRF_REVIEW.COVERAGE_LEVELS.map((c) => (
                        <option key={c} value={c}>{Math.round(c * 100)}%</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={lab}>Productivity</label>
                    <input value={ngDraft.prod} onChange={(e) => setD("prod", e.target.value)}
                           onFocus={(e) => e.target.select()} inputMode="decimal" placeholder="1.00" style={inp} />
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <label style={lab}>Interval election — percent of value per interval</label>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3, minmax(0,1fr))" : "repeat(11, minmax(0,1fr))", gap: 5 }}>
                    {PRF_REVIEW.LAB.map((iv) => (
                      <div key={iv}>
                        <div style={{ fontSize: 10, color: C.muted, textAlign: "center", marginBottom: 2 }}>{iv}</div>
                        <input value={ngDraft.alloc[iv] == null ? "" : Math.round(ngDraft.alloc[iv] * 100)}
                               onChange={(e) => {
                                 const raw = e.target.value.trim();
                                 setNgDraft((d) => {
                                   const a = { ...d.alloc };
                                   if (raw === "") delete a[iv]; else a[iv] = Number(raw) / 100;
                                   return { ...d, alloc: a };
                                 });
                               }}
                               onFocus={(e) => e.target.select()} inputMode="numeric"
                               style={{ ...inp, textAlign: "center", padding: "4px 2px", fontSize: 12 }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, marginTop: 5, color: allocPct === 100 ? C.muted : C.cost }}>
                    Elected {allocPct}% of 100%
                    {allocPct !== 100 ? " — RMA requires the intervals to total 100%." : ""}
                  </div>
                </div>

                {/* ⚠ THE PRACTICE SETS THE COUNTY BASE VALUE — $447 non-irrigated
                    against $330 irrigated on a real book grid, and NON-irrigated
                    is the higher tier, which is the direction anyone reasoning
                    from "irrigated ground is worth more" gets backwards. It is
                    offered only once the county, the use and the intervals name
                    one set of bands, and it is picked rather than defaulted. */}
                {(ngResult?.choices || []).length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <label style={lab}>Irrigation practice — it sets the county base value</label>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {ngResult.choices.map((c) => {
                        const on = c.families.includes(Number(ngDraft.family));
                        return (
                          <button key={c.label}
                                  title={`RMA band ${c.families.map((f) => f + "xx").join(", ")}`}
                                  onClick={() => setD("family", on ? "" : c.families[0])}
                                  style={{ padding: "5px 11px", borderRadius: 999, cursor: "pointer",
                                           fontSize: 12, fontWeight: 700,
                                           border: `1px solid ${on ? "#2d4a2d" : C.line}`,
                                           background: on ? "#2d4a2d" : "#fff", color: on ? "#fff" : C.ink2 }}>
                            {c.label}{c.cbv != null ? ` · ${D(c.cbv)}/ac` : " · not published here"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 10, fontSize: 12.5, color: ngResult?.ready ? "#2d4a2d" : C.cost }}>
                  {ngResult?.ready
                    ? `Grid ${ngGrid} is priced below — nothing was assumed for it.`
                    : `Still needed before this can be priced: ${(ngResult?.missing || []).join(", ")}.`}
                </div>
              </>
            );
          })()}
        </div>
      )}


      {review?.fatal === "NO_CBV" && (
        <div style={{ ...card, borderColor: C.cost }}>
          <b>County base value not loaded for grid {review.grid}.</b>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            Nothing on this page can be priced without RMA's published county base value, so nothing is shown
            rather than a figure derived from something else.
          </div>
        </div>
      )}

      {review && !review.fatal && block && (
        <>
          {/* identity + block picker */}
          <div style={{ ...card, marginBottom: 14 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "baseline", justifyContent: "space-between" }}>
              <div style={{ fontSize: 15, color: C.ink }}>
                <b>{activeFarmer?.name}</b>
                <span style={{ color: C.muted }}>
                  {"  ·  "}{data.policy.policy_number}{"  ·  "}CY{data.policy.crop_year}
                  {"  ·  "}Grid <b style={{ color: C.ink }}>{block.grid}</b>
                  {block.county ? `  ·  ${block.county}` : ""}
                  {review.cbv_resolution?.intended_use
                    ? <>{"  ·  "}<b style={{ color: C.ink }}>{review.cbv_resolution.intended_use}</b></>
                    : <>{"  ·  "}<span style={{ color: C.cost }}>intended use unresolved</span></>}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {allBlocks.length > 1 && (
                  <select value={blockKey || ""} onChange={(e) => { setBlockKey(e.target.value); setSimAlloc(null); setSimCov(null); }}
                          style={{ padding: "5px 8px", border: `1px solid ${C.line}`, borderRadius: 6, fontSize: 13, background: "#fff" }}>
                    {allBlocks.map((b) => (
                      <option key={b.key} value={b.key}>
                        Grid {b.grid} · {b.acres == null ? "acres not entered" : Math.round(b.acres) + " ac"}{b.intended_use ? " · " + b.intended_use : ""}
                        {b.is_new_grid ? " · not on this policy" : ""}
                        {b.key === blockKey && !b.is_new_grid && review?.cbv_resolution?.intended_use &&
                         b.intended_use && review.cbv_resolution.intended_use !== b.intended_use
                          ? " (priced as " + review.cbv_resolution.intended_use + ")" : ""}
                      </option>
                    ))}
                  </select>
                )}
                {/* Quoting a grid the customer does not insure is prospecting.
                    It reads no farmer data — the actuarials are RMA's published
                    tables — but it is the agency's tool and not a customer's. */}
                {!isPortal && (
                <button onClick={() => setNgOpen((v) => !v)}
                        style={{ padding: "5px 10px", border: `1px solid ${C.line}`, borderRadius: 6,
                                 fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                                 background: ngOpen ? "#2d4a2d" : "#fff", color: ngOpen ? "#fff" : C.ink2 }}>
                  {ngGrid ? `Grid ${ngGrid}` : "Quote another grid"}
                </button>
                )}
              </div>
            </div>
            {!block.allocation_sums && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: C.cost }}>
                ⚠ The stored interval elections total {PC(block.allocation_total)}, not 100%. Shown as loaded — not normalised,
                because normalising would invent an election the grower never made.
              </div>
            )}
            {(review.cbv_resolution?.m13_inconsistent ||
              (block.intended_use && review.cbv_resolution?.intended_use &&
               block.intended_use !== review.cbv_resolution.intended_use)) && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: C.cost, lineHeight: 1.6, maxWidth: "78ch" }}>
                ⚠ <b>The AIP's file disagrees with itself on this block.</b> M13 labels these acres{" "}
                <b>{review.cbv_resolution?.m13_inconsistent?.m13_use || block.intended_use}</b>, but the liability
                the AIP itself stored only works out against the{" "}
                <b>{review.cbv_resolution.intended_use}</b> county base value — the two are about five times apart,
                so this is not a rounding difference.
                {review.cbv_resolution?.m13_inconsistent ? (
                  <> M13 implies ${review.cbv_resolution.m13_inconsistent.m13_implies_cbv}/ac against a stored
                  ${Math.round(review.cbv_resolution.m13_inconsistent.liability_implies_cbv * 100) / 100}/ac.</>
                ) : null}
                {" "}The stored liability is treated as authoritative and this block prices as{" "}
                <b>{review.cbv_resolution.intended_use}</b>. Nothing in the file was changed — the disagreement is
                shown, not resolved.
              </div>
            )}
            {review.cbv_resolution && review.cbv_resolution.ok === false && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: C.cost }}>
                ⚠ The county base value could not be established from RMA's published table
                ({review.cbv_resolution.status}). Nothing here is priced from a substituted figure.
              </div>
            )}
            {review.limits_status !== "OK" && !review.current?.error && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: C.cost }}>
                ⚠ RMA percent-of-value limits are not loaded for grid {block.grid}. Allocations are priced but
                <b> not verified as legal</b>, and the optimizer produces nothing.
              </div>
            )}
          </div>

          {/* section tabs */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            {SECTIONS.map(([k, label]) => (
              <button key={k} onClick={() => setSection(k)}
                      style={{ padding: "7px 13px", borderRadius: 6, fontSize: 13, cursor: "pointer",
                               border: `1px solid ${section === k ? C.ink2 : C.line}`,
                               background: section === k ? C.ink2 : "#fff",
                               color: section === k ? "#fff" : C.ink2, fontWeight: section === k ? 600 : 400 }}>
                {label}{k === "optimizer" ? " ⚗" : ""}
              </button>
            ))}
          </div>

          {/* THREE JOBS, NO ADVICE. What the policy is, what a renewal costs,
              and the history. The interval assessment that used to sit here —
              per-lookback ranks, stability verdicts, a standing per interval —
              was removed on 2026-09-02: it read as a recommendation on a page
              that is deliberately a quoter, and it was confusing on screen.
              The evidence matrix it sat above survives on its own tab. */}
          {/* ⚠ `!isPortal` is the SECOND guard, not a duplicate of the tab list.
              A stored section, a deep link or a future route can all put a
              customer on this key; the tab row only stops the ones that click. */}
          {/* ⚠ THESE SECTIONS ARE CALLED, NOT RENDERED AS ELEMENTS, AND THAT IS
              WHAT MAKES THE QUOTER TYPEABLE.

              Every one of them is declared INSIDE PRFReviewView, so the function
              is a NEW object on every render of this view. Written <RenewalSection />
              React compares that new type against the old one, decides it is a
              different component, and UNMOUNTS AND REMOUNTS the whole subtree —
              destroying the <input> DOM node and the caret inside it.

              Kevin, 2026-09-05: "I click in box and as soon as I type one digit,
              it freezes and I cant keep typing without re clicking in the box."
              That is exactly this. The acres, productivity and the eleven interval
              boxes all write PARENT state (setQAcres / setQProd / setPct), so the
              first keystroke re-renders PRFReviewView, remounts this section, and
              the field the agent is typing into stops existing. It is not a
              validation rule and not a slow render — the element is gone.

              Calling the function inlines its JSX into this render instead, so
              there is no child component to remount and the input keeps its
              identity across keystrokes.

              ⚠ ONLY HOOK-FREE SECTIONS MAY BE CALLED. A function that calls a
              hook and is invoked CONDITIONALLY (as all of these are) would run
              hooks in a different order between renders, which is the Rules of
              Hooks violation React cannot recover from. PolicyOverview (useMemo)
              and CoverageSection (useState) therefore stay as elements — neither
              writes parent state from an input, so neither shows this defect.
              Hoisting those out of the view is the real fix and is not attempted
              here. */}
          {section === "quote" && !isPortal && <><PolicyOverview />{RenewalSection()}</>}
          {/* ⚠ THE POLICY SUMMARY IS NOT PART OF THE QUOTER, AND DROPPING IT WITH
              THE QUOTE TAB WOULD BE A REGRESSION. It is the plainest statement of
              what this grower actually bought — grids, counties, acres, level,
              productivity, protection, premium — and in portal it moves here
              rather than disappearing. */}
          {section === "coverage" && isPortal && <PolicyOverview />}
          {section === "coverage" && <CoverageSection />}
          {section === "history" && HistorySection()}
          {/* "current" has no tab button, but picking another customer in the
              picker above lands on it, so it is live. The "simulator" what-if,
              reachable only through a stale saved section, was cut 2026-11-02
              (a stored "simulator" now falls back to the default above). */}
          {section === "current" && <><PolicyOverview />{CurrentSection()}</>}
          {section === "history" && !isPortal && advanced && OptimizerSection()}
        </>
      )}
    </div>
  );

  // ── 1. CURRENT COVERAGE ───────────────────────────────────────────
  // ── THE WHOLE POLICY, ON ONE PAGE ─────────────────────────────────────────
  // A farm's PRF is one policy that can carry several blocks, and the screen
  // made you pick one from a dropdown to see anything at all. So the only way to
  // learn what the POLICY costs or protects was to visit each grid and add them
  // up by hand — and nothing said how many blocks there were, so a farm with
  // four looked exactly like a farm with one.
  //
  // Every block is priced through the SAME priceAndReplay the block detail below
  // uses (see policyOverview's own note on why the context builder was extracted
  // rather than copied), so the total and the per-grid figures cannot disagree.
  // ⚠ THIS MEMO LIVES INSIDE THE COMPONENT, NOT BESIDE THE OTHER SECTIONS.
  // Everything below the enclosing component's `return` is a hoisted FUNCTION
  // DECLARATION, which is why the sections can be written there at all. A
  // `const` written in the same place never executes — statements after a
  // return do not run — so a hoisted section reading it hit the temporal dead
  // zone and the whole page rendered as a header over empty space. check-tdz
  // did not catch it and check-screens called the page "clean", because a blank
  // body is still a body; only the screenshot showed it.
  function PolicyOverview() {
    const overview = useMemo(() => {
    if (!data?.blocks?.length) return null;
    return PRF_REVIEW.policyOverview(data.blocks, (b) => {
      const refs = data.refsByGrid[b.grid];
      if (!refs || !refs.cbvRows?.length) return null;
      const solveIv = (data.ivRows || []).find((r) =>
        Number(r.grid_id) === b.grid && Number(r.insured_acres) === b.acres && Number(r.liability) > 0);
      return {
        ...refs,
        cbvIndex: PRF_REVIEW.indexCbv(refs.cbvRows),
        fpiIndex: PRF_REVIEW.indexFieldPrf(data.fpiRows),
        policyNumber: data.policy.policy_number,
        stateCode: data.policy.state || null,
        solveLiability: solveIv ? Number(solveIv.liability) : null,
        solvePercent: solveIv ? Number(solveIv.percent_value) : null,
        bfrBonus: data.bfrBilled?.bonus || 0,
      };
    }, data.prfPol || null);
  }, [data]);

    if (!overview || overview.blocks_total < 1) return null;
    const t = overview.totals;
    // ⚠ AN IN-FORCE POLICY PRINTS WHAT IT WAS BILLED. `shown` is the AIP's own
    // total where the policy carries one; our re-pricing is the check beside it.
    const sh = overview.shown || t;
    const chk = overview.billed_check;
    const many = overview.blocks_total > 1;
    const th2 = { ...th, fontSize: 10.5 };
    return (
      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, fontWeight: 700 }}>
            Whole policy · {overview.blocks_total} {overview.blocks_total === 1 ? "block" : "blocks"}
            {t.grids.length > 1 ? ` · ${t.grids.length} grids` : ` · grid ${t.grids[0]}`}
            {t.counties.length ? ` · ${t.counties.join(", ")}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 12 }}>
          <div><div style={{ fontSize: 20, fontWeight: 800, color: C.ink }}>{t.acres.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
               <div style={{ fontSize: 12, color: C.muted }}>insured acres</div></div>
          <div><div style={{ fontSize: 20, fontWeight: 800, color: C.ink }}>{D(t.protection)}</div>
               <div style={{ fontSize: 12, color: C.muted }}>protection</div></div>
          <div><div style={{ fontSize: 20, fontWeight: 800, color: C.ink }}>{D(sh.producer_premium)}</div>
               <div style={{ fontSize: 12, color: C.muted }}>producer premium<span style={{ color: C.muted }}> · of {D(sh.total_premium)} total{sh.source === "BILLED" ? " · as billed" : ""}</span></div></div>
          {/* ⚠ COVERAGE, PRODUCTIVITY AND THE BASE VALUE ARE NOT ADDITIVE. A farm
              can genuinely carry 85% on one grid and 90% on another; averaging
              them would print a figure that appears on no statement. Where the
              blocks agree the shared value is shown, and where they differ this
              says "varies" and the rows below carry the truth. */}
          <div><div style={{ fontSize: 20, fontWeight: 800, color: t.coverage_level == null ? C.cost : C.ink }}>
                 {t.coverage_level == null ? "varies" : PC(t.coverage_level)}</div>
               <div style={{ fontSize: 12, color: C.muted }}>coverage level</div></div>
          <div><div style={{ fontSize: 20, fontWeight: 800, color: t.productivity_factor == null ? C.cost : C.ink }}>
                 {t.productivity_factor == null ? "varies" : PC(t.productivity_factor)}</div>
               <div style={{ fontSize: 12, color: C.muted }}>productivity</div></div>
        </div>

        {many && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, minWidth: 780 }}>
              <thead><tr>
                <th style={th0}>Grid</th><th style={th0}>County</th><th style={th0}>Use</th>
                <th style={th2}>Acres</th><th style={th2}>Level</th><th style={th2}>Prod.</th>
                <th style={th2}>CBV</th><th style={th2}>Protection</th><th style={th2}>Producer prem.</th>
                <th style={th0}>Intervals</th>
              </tr></thead>
              <tbody>
                {overview.rows.map((r, i) => {
                  const on = r.block.key === block?.key;
                  return (
                    <tr key={r.block.key + i}
                        onClick={() => setBlockKey(r.block.key)}
                        style={{ background: on ? "#eef4ee" : undefined, cursor: "pointer" }}
                        title="Show this block in the sections below">
                      <td style={{ ...td0, fontWeight: on ? 700 : 500 }}>{r.block.grid}</td>
                      {/* Name first. Where only the rate's county CODE is known it is
                          LABELLED as a code rather than printed bare — "113" in a
                          column headed COUNTY reads as a county called 113. */}
                      <td style={td0}>{r.county || (r.county_code ? "county code " + r.county_code : "—")}</td>
                      <td style={td0}>{r.intended_use || "—"}</td>
                      <td style={td}>{Number(r.block.acres).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td style={td}>{PC(r.block.coverage_level)}</td>
                      <td style={td}>{PC(r.block.productivity_factor)}{Number(r.block.share) > 0 && Number(r.block.share) < 1
                        ? <span style={{ color: C.muted }}>{" "}· {Math.round(r.block.share * 1000) / 10}% share</span> : null}</td>
                      {r.ok ? <>
                        <td style={td}>{r.cbv == null ? "—" : "$" + r.cbv}</td>
                        <td style={td}>{D(r.priced.protection)}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{D(r.priced.producer_premium)}</td>
                      </> : (
                        /* Listed with its reason — a policy total quietly missing
                           a grid reads as a smaller farm, not as a short read. */
                        <td style={{ ...td, textAlign: "left", color: C.cost }} colSpan={3}>{r.says}</td>
                      )}
                      <td style={{ ...td0, fontSize: 11.5, color: C.muted }}>{r.block.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, lineHeight: 1.6, maxWidth: "88ch" }}>
          {many
            ? <>Every block on this policy, priced through the same engine as the detail below — click a row to load it
                into the sections beneath. Acres, protection and premium add up; coverage level, productivity factor and
                county base value are per-block elections and are never averaged.</>
            : <>This policy is written as a single block, so the totals above are that block.</>}
          {overview.unpriceable.length > 0 && (
            <> <b style={{ color: C.cost }}>{overview.unpriceable.length} of {overview.blocks_total} blocks
              could not be priced</b>, so the totals above are the sum of {overview.blocks_priced} — the rows say why.</>
          )}
          {data.bfrBilled?.bonus > 0 && (
            <> Premium includes the beginning farmer and rancher subsidy (+{Math.round(data.bfrBilled.bonus * 100)} points, read from the bill).</>
          )}
          {!isPortal && chk && !chk.agrees && (
            <> <b style={{ color: C.cost }}>Our re-pricing does not reproduce the bill</b>
              {" "}({Object.entries(chk.gaps).filter(([, v]) => Math.abs(v) >= 1).map(([k, v]) => `${k.replace(/_/g, " ")} ${Dsig(v)}`).join(", ") || "not every block priced"}) — the premium shown is the AIP's.</>
          )}
          {t.stored_liability > 0 && (
            <> The AIP's own stored liability across these blocks is {D(t.stored_liability)}
              {Math.abs(t.stored_liability - t.protection) > 1
                ? <>, which differs from ours by {D(Math.abs(t.protection - t.stored_liability))} — a disagreement worth
                    reading per block below, not a rounding.</>
                : <>, which agrees with ours.</>}</>
          )}
        </div>
      </div>
    );
  }

  function CurrentSection() {
    const c = review.current;
    if (c.error) {
      const res = review.cbv_resolution || {};
      return (
        <div style={{ ...card, borderColor: C.cost }}>
          <b style={{ color: C.cost }}>This block cannot be priced.</b>
          <div style={{ fontSize: 13, color: C.ink2, marginTop: 6, lineHeight: 1.6, maxWidth: "72ch" }}>
            {res.status === "PRF_USE_UNRESOLVED"
              ? "Haying and grazing acres could not be separated for this block. They carry different county base values, so pricing them together would be wrong for one half."
              : res.status === "PRF_CBV_NOT_PUBLISHED"
              ? "RMA publishes no county base value matching this block for that grid and crop year."
              : res.status === "PRF_PRACTICE_UNRESOLVED"
              ? "Several practices apply here and they publish different county base values, so the right one is not established."
              : c.error === "RATE_AMBIGUOUS"
              ? "More than one premium rate applies to this block and they disagree, so none was used."
              : c.error === "NO_RATE"
              ? "RMA premium rates are not loaded for this grid and crop year."
              : "The actuarial identity could not be closed from authoritative data."}
            {res.detail ? <div style={{ marginTop: 6, color: C.muted }}>{res.detail}</div> : null}
            {c.intervals?.length ? <div style={{ marginTop: 6, color: C.muted }}>Intervals affected: {c.intervals.join(", ")}</div> : null}
            <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>
              Status <code style={{ background: "#eef1ee", padding: "1px 5px", borderRadius: 3 }}>{res.status || c.error}</code>.
              Nothing is substituted — a figure is shown only when RMA's own values support it.
            </div>
          </div>
        </div>
      );
    }
    const storedGap = review.stored.liability ? c.protection - review.stored.liability : null;
    const Stat = ({ label, value, sub }) => (
      <div style={{ minWidth: 130 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted }}>{label}</div>
        <div style={{ fontSize: 19, fontWeight: 600, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{value}</div>
        {sub && <div style={{ fontSize: 11.5, color: C.muted }}>{sub}</div>}
      </div>
    );
    return (
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 14 }}>
        <div style={card}>
          {data.blocks.length > 1 ? null : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 22, marginBottom: 14 }}>
            <Stat label="Insured acres" value={block.acres.toLocaleString("en-US")} />
            <Stat label="Coverage level" value={PC(block.coverage_level)} />
            <Stat label="Productivity" value={PC(review.block?.productivity_factor ?? block.productivity_factor)}
                  sub={Number(review.block?.share) > 0 && Number(review.block.share) < 1 ? `share ${Math.round(review.block.share * 1000) / 10}%` : undefined} />
            <Stat label="County base value"
                  value={review.cbv_resolution?.cbv != null ? "$" + Math.round(review.cbv_resolution.cbv) : "—"}
                  sub={review.cbv_resolution?.source === "DERIVED_IDENTITY" ? "per acre · solved from the AIP's liability"
                     : review.cbv_resolution?.source === "DERIVED_OVER_INCONSISTENT_M13" ? "per acre · solved (M13 disagrees)"
                     : review.cbv_resolution?.source === "M13_IDENTITY" ? "per acre · RMA published"
                     : review.cbv_resolution?.is_dollar_rounded ? "per acre · approximate (dollar-rounded)"
                     : "per acre"} />
            <Stat label="Protection" value={D(c.protection)} />
            {(() => {
              // One block IS the policy, so the bill is this block's bill.
              const bill = PRF_REVIEW.billedTotals(data.prfPol);
              return bill && bill.producer_premium != null
                ? <Stat label="Producer premium" value={D(bill.producer_premium)} sub={`of ${D(bill.total_premium)} total · as billed`} />
                : <Stat label="Producer premium" value={D(c.producer_premium)} sub={`of ${D(c.total_premium)} total`} />;
            })()}
          </div>
          )}
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 5 }}>
            Interval allocation
          </div>
          <AllocBar alloc={block.allocation} height={40} />
          {storedGap != null && Math.abs(storedGap) >= 1 && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: C.cost }}>
              ⚠ Our priced protection differs from the stored liability by {Dsig(storedGap)} ({D(review.stored.liability)} on file).
              Both are shown rather than reconciled — a disagreement with the AIP's own figure is a finding, not a rounding.
            </div>
          )}
        </div>

        <div style={card}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 8 }}>
            How this structure behaved over RMA's published record
          </div>
          {!c.years.length ? (
            <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.6, maxWidth: "74ch" }}>
              RMA's published index history for the <b>CY{data.policy.crop_year} vintage</b> is not loaded, so this
              structure has not been replayed against it. Nothing here is filled in from another year: RMA
              restates the index history every crop year — the normal is a mean over a window that advances —
              so another year's record is a different record, not a stand-in for this one.
            </div>
          ) : (<>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 22 }}>
            <Stat label="Years" value={c.years.length} sub={`${c.years[0]}–${c.years[c.years.length - 1]}`} />
            <Stat label="Paid in" value={PC(c.pay_freq)} sub="of those years" />
            <Stat label="Avg indemnity" value={D(c.avg_indemnity)} sub="per year" />
            <Stat label="Avg net" value={D(c.avg_net)} sub="after premium" />
            <Stat label="Best year" value={D(c.best_net)} />
            <Stat label="Worst year" value={D(c.worst_net)} sub="a year that paid nothing" />
          </div>
          {review.severity_available && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: C.ink2, maxWidth: "70ch", lineHeight: 1.55 }}>
              Of those {c.years.length} years, <b>{review.severity_counts.SEVERE}</b> are the lowest-index years on this
              grid's core season and <b>{review.severity_counts.POOR}</b> the next-lowest. Those are <b>rainfall-index</b>
              {" "}years, not observed forage losses — no crop outcome was measured.
            </div>
          )}
          </>)}
        </div>
      </div>
    );
  }

  function CoverageSection() {
    // THE WINDOW IS THE ORGANIZING CONTROL, exactly as it already is on Interval
    // History — one concept, one helper (PRF_IH.windowYears), so the two tabs cannot
    // disagree about which years "the last 10" means. Kevin, 2026-09-02: "what we need
    // to see is loss ratios by selected years. If I had this the last 10 years, what
    // was my loss ratio?"
    const [lrPeriod, setLrPeriod] = React.useState(10);
    // The ladder follows the QUOTE YEAR. `review.coverage` is the policy year's
    // and is the fallback only when the quote cannot resolve a year's rates —
    // never a silent substitute, because a CY2026 premium under a CY2027
    // heading is the disagreement this replaced.
    const cs = renewal?.ladder || review.coverage;
    const csYear = renewal?.ladder ? renewal.year : renewal?.policyYear;
    const csFellBack = !!(renewal && !renewal.ladder);
    if (!cs) return <div style={{ ...card, color: C.cost }}>Coverage levels cannot be priced for this block.</div>;
    return (
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 14 }}>
        <div style={card}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 10 }}>
            Protection bought and producer premium paid, at each level
            {csYear ? <> — <b style={{ color: C.ink2 }}>CY{csYear} rates</b></> : null}
          </div>
          {csFellBack && (
            <div style={{ fontSize: 11.5, color: "#8a5a00", marginBottom: 8, lineHeight: 1.5 }}>
              CY{renewal.year} could not be priced for this block, so this is CY{renewal.policyYear}.
              The Quote tab says why.
            </div>
          )}
          {(() => {
            const av = cs.levels.filter((r) => r.available);
            const maxP = Math.max(1, ...av.map((r) => Number(r.protection) || 0));
            const maxC = Math.max(1, ...av.map((r) => Number(r.producer_premium) || 0));
            // ⚠ ONE SHARED SCALE MADE THE PREMIUM BAR A SLIVER THAT SAID NOTHING.
            // Producer premium runs 1.5-5% of protection, so drawn on protection's
            // scale every orange bar was 2-5 pixels and identical between levels —
            // it read as a rendering fault rather than as a subsidy. Kevin, 2026-09-02:
            // "the one horizontal bar chart looks goofy".
            //
            // Each series now has its OWN scale, so the bars compare LEVELS within a
            // colour, which is the comparison a reader actually makes going up the
            // ladder. The thing the shared scale was trying to show — how little of
            // the protection you pay for — is stated as a NUMBER instead, per $100 of
            // protection, which is checkable where a sliver was not.
            return (
              <div style={{ display: "grid", gap: 7 }}>
                {av.map((r) => (
                  <div key={r.coverage} style={{ display: "grid", gridTemplateColumns: isMobile ? "42px 1fr" : "56px 1fr", gap: 10, alignItems: "center" }}>
                    <div style={{ fontSize: 12.5, fontWeight: r.is_current ? 700 : 500, color: r.is_current ? C.ink : C.ink2, fontVariantNumeric: "tabular-nums" }}>
                      {PC(r.coverage)}
                    </div>
                    <div style={{ display: "grid", gap: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 11, background: "#eef1ee", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: (100 * (Number(r.protection) || 0) / maxP) + "%", height: "100%", background: r.is_current ? "#2d4a2d" : "#8fae8f" }} />
                        </div>
                        <div style={{ fontSize: 11.5, color: C.ink2, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", textAlign: "right", width: isMobile ? 76 : 96 }}>
                          {D(r.protection)}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 7, background: "#f6efe9", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: (100 * (Number(r.producer_premium) || 0) / maxC) + "%", height: "100%", background: "#c2551f", opacity: r.is_current ? 1 : 0.72 }} />
                        </div>
                        <div style={{ fontSize: 11, color: "#c2551f", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", textAlign: "right", width: isMobile ? 76 : 96 }}>
                          {D(r.producer_premium)}
                          {Number(r.protection) > 0 ? <span style={{ color: C.muted }}>{" "}· {D(100 * (Number(r.producer_premium) || 0) / Number(r.protection))}/$100</span> : null}
                        </div>
                      </div>
                      {r.is_current ? <div style={{ fontSize: 10.5, fontWeight: 700, color: C.ink }}>← today</div> : null}
                    </div>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.5, maxWidth: "78ch" }}>
                  Green is protection, orange is what the producer pays. <b>The two colours are on separate
                  scales</b> — compare green against green and orange against orange, never across. What you pay
                  per $100 of protection is printed beside the premium, which is the subsidy stated as a number.
                </div>
              </div>
            );
          })()}
        </div>
        {(() => {
          // The record every row is replayed over. Taken from the ladder itself so the
          // window can never name a year the replay did not price.
          const anyRow = cs.levels.find((r) => r.available && (r.years || []).length);
          const allYears = anyRow ? anyRow.years : [];
          const win = PRF_IH.windowYears(allYears, lrPeriod, null);
          const lr = {};
          for (const r of cs.levels) if (r.available) lr[r.coverage] = PRF_IH.lossRatioOver(r, win);
          const cur = cs.levels.find((r) => r.available && r.is_current);
          const curLr = cur ? lr[cur.coverage] : null;
          const LR = (v) => v == null ? "—" : v.toFixed(2) + "×";
          return (
        <div style={card}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline", marginBottom: 10 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted }}>
              If this election had been in force — replayed over
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {PRF_IH.PERIODS.map((p) => (
                <button key={String(p)} onClick={() => setLrPeriod(p)} style={pill(lrPeriod === p)}>
                  {p === "all" ? `all ${allYears.length} yrs` : `last ${p}`}
                </button>
              ))}
            </div>
            {win.length ? (
              <div style={{ fontSize: 11.5, color: C.ink2 }}>
                {win[0]}–{win[win.length - 1]} · <b>{win.length}</b> yrs of RMA's published index
              </div>
            ) : null}
          </div>
          {/* ⚠ THE INDEX IS HISTORY AND THE PREMIUM IS TODAY'S. Saying "your loss ratio
              was 1.4x" would claim something we cannot source — the premiums and
              elections actually held each year are not in anything we hold. */}
          <div style={{ fontSize: 12, color: C.ink2, lineHeight: 1.55, marginBottom: 10, maxWidth: "80ch" }}>
            <b>Loss ratio is indemnity returned per $1 of producer premium</b>, and this is a REPLAY, not a
            statement of history: the rainfall index is RMA's published record, the premium and protection are
            <b> this year's quote held constant</b>. It is what this election would have returned, not what the
            grower's own loss ratio was. Above 1.00× means it returned more than it cost.
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, minWidth: 640 }}>
              <thead><tr>
                <th style={th0}>Level</th><th style={th}>Protection</th><th style={th}>Producer premium</th>
                <th style={th}>Loss ratio</th><th style={th}>Avg indemnity</th><th style={th}>Avg net</th>
                <th style={th}>Paid in</th><th style={th}>vs current</th>
              </tr></thead>
              <tbody>
                {cs.levels.map((r) => r.available ? (
                  <tr key={r.coverage} style={{ background: r.is_current ? "#eef4ee" : undefined, fontWeight: r.is_current ? 600 : 400 }}>
                    <td style={td0}>{PC(r.coverage)}{r.is_current ? " ← today" : ""}</td>
                    <td style={td}>{D(r.protection)}</td>
                    <td style={td}>{D(r.producer_premium)}</td>
                    <td style={{ ...td, fontWeight: 700, color: lr[r.coverage]?.loss_ratio == null ? C.muted : lr[r.coverage].loss_ratio >= 1 ? C.gain : C.ink2 }}>
                      {LR(lr[r.coverage]?.loss_ratio)}
                    </td>
                    <td style={td}>{D(lr[r.coverage]?.avg_indemnity)}</td>
                    <td style={td}>{lr[r.coverage] ? D(lr[r.coverage].avg_indemnity - r.producer_premium) : "—"}</td>
                    <td style={td}>{lr[r.coverage] ? `${lr[r.coverage].years_paid}/${lr[r.coverage].n_years}` : "—"}</td>
                    <td style={td}>{r.is_current ? "—" : (lr[r.coverage] && curLr
                      ? Dsig((lr[r.coverage].avg_indemnity - r.producer_premium) - (curLr.avg_indemnity - cur.producer_premium))
                      : "—")}</td>
                  </tr>
                ) : (
                  <tr key={r.coverage}><td style={td0}>{PC(r.coverage)}</td>
                    <td style={{ ...td, textAlign: "left", color: C.muted }} colSpan={7}>not rated for this allocation ({r.reason})</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {curLr && curLr.loss_ratio_gross != null && (
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.5, maxWidth: "80ch" }}>
              At the current level that is <b>{curLr.loss_ratio.toFixed(2)}×</b> against producer premium
              and <b>{curLr.loss_ratio_gross.toFixed(2)}×</b> against gross premium — the second is RMA's own
              actuarial definition and is always the smaller, because the subsidy sits in its denominator.
            </div>
          )}
        </div>
          ); })()}
        <div style={{ ...card, background: "#fbfcfb" }}>
          <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.6, maxWidth: "70ch" }}>
            <b>No level is marked as the answer, and that is a measurement rather than caution.</b> Searched across
            all 61 PRF policies in the book, every one landed on 90% — because RMA's subsidy falls more slowly than
            the rate rises, so the top of the ladder wins on return AND on payment frequency at once. That is a
            property of the rate tables, not a finding about this farm.
            <br /><br />
            The allocation is held constant here; only the level moves. The two history columns are the trade:
            a higher level costs more in the years that do not pay and returns more in the years the index was lowest.
          </div>
        </div>
      </div>
    );
  }

  // ── HISTORY — the year x interval evidence, on its own ────────────
  // The one PRF visual Kevin uses. It was the bottom half of an interval
  // assessment that ranked and graded; that half is gone and this is now the
  // whole tab, open by default rather than behind a disclosure.
  function HistorySection() {
    if (!matrix) return <div style={{ ...card, color: C.muted }}>No index history for this block.</div>;
    return (
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 14 }}>
        {/* LEVEL 3 — the complete year x interval evidence */}
        <div style={card}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>Historical evidence</div>
            <button onClick={() => setMatrixOpen((v) => !v)}
                    style={{ fontSize: 12, fontWeight: 700, padding: "5px 10px", borderRadius: 6, cursor: "pointer",
                             border: `1px solid ${C.line}`, background: "#fff", color: C.ink2 }}>
              {matrixOpen ? "Hide" : "Show"} every year × interval
            </button>
          </div>
          {!matrixOpen && matrix?.available && (
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 6 }}>
              {matrix.rows.length} years ({matrix.year_range.from}–{matrix.year_range.to}) × {matrix.intervals.length} intervals,
              every valid interval and not only the elected ones.
            </div>
          )}
          {matrixOpen && !matrix?.available && (
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 8 }}>{matrix?.reason || "No history for this grid."}</div>
          )}
          {matrixOpen && matrix?.available && (() => {
            const view = matrix.views.find((v) => v.id === matrixView) || matrix.views[0];
            const isRain = view.id === "rainfall";
            return (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {matrix.views.map((v) => (
                    <button key={v.id} onClick={() => setMatrixView(v.id)}
                            style={{ fontSize: 11.5, fontWeight: 700, padding: "4px 10px", borderRadius: 999, cursor: "pointer",
                                     border: `1px solid ${matrixView === v.id ? C.ink2 : C.line}`,
                                     background: matrixView === v.id ? C.ink2 : "#fff",
                                     color: matrixView === v.id ? "#fff" : C.ink2 }}>{v.label}</button>
                  ))}
                </div>
                {/* ⚠ WHAT THIS CHANGES IS WHAT PRINTS. The same memo feeds the quote
                    package, so there is no second layout to keep in step — and no
                    "apply" button, because a setting that has to be confirmed is a
                    setting somebody forgets to confirm before hitting print. */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 8,
                              padding: "8px 10px", border: `1px solid ${C.line}`, borderRadius: 8, background: "#fafcfa" }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: C.ink2, textTransform: "uppercase", letterSpacing: ".04em" }}>Intervals</span>
                  {PRF_EM.LAB.map((l) => {
                    const on = !mxIvs || mxIvs.includes(l);
                    return (
                      <button key={l}
                        onClick={() => setMxIvs((prev) => {
                          const cur = prev && prev.length ? prev : PRF_EM.LAB.slice();
                          const next = cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l];
                          // ⚠ REFUSE THE EMPTY SET rather than storing it. Every interval
                          // switched off is a table of one column headed Year, which reads
                          // as a broken screen; the last one stays on.
                          if (!next.length) return cur;
                          return next.length === PRF_EM.LAB.length ? null : PRF_EM.LAB.filter((x) => next.includes(x));
                        })}
                        title={on ? "Showing " + l + " — click to hide" : "Hidden — click to show " + l}
                        style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 999, cursor: "pointer",
                                 border: `1px solid ${on ? C.ink2 : C.line}`,
                                 background: on ? C.ink2 : "#fff", color: on ? "#fff" : C.muted }}>{l}</button>
                    );
                  })}
                  {/* ⚠ NO SEASON PRESET. An Apr-Sep button shipped here first and was
                      removed the same day: Kevin named those months as an EXAMPLE of a
                      selection, not as the selection. A preset on a page whose whole
                      point is that the agent chooses reads as the recommended window,
                      and this is the PRF page, where a suggested interval set is a
                      suggested bet. The chips already select freely; "All" is the only
                      button, because returning to the untouched state is not a view. */}
                  <button onClick={() => setMxIvs(null)} disabled={!mxIvs}
                          style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 6,
                                   cursor: mxIvs ? "pointer" : "default",
                                   border: `1px solid ${C.line}`, background: "#fff", color: mxIvs ? C.ink2 : C.muted }}>All</button>
                  <span style={{ width: 1, height: 18, background: C.line, margin: "0 2px" }} />
                  <span style={{ fontSize: 11, fontWeight: 800, color: C.ink2, textTransform: "uppercase", letterSpacing: ".04em" }}>Years</span>
                  {[10, 15, 20, 30].map((nY) => (
                    <button key={nY} onClick={() => setMxYrs(mxYrs === nY ? null : nY)}
                            style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 6, cursor: "pointer",
                                     border: `1px solid ${mxYrs === nY ? C.ink2 : C.line}`,
                                     background: mxYrs === nY ? C.ink2 : "#fff", color: mxYrs === nY ? "#fff" : C.ink2 }}>{nY}</button>
                  ))}
                  <button onClick={() => setMxYrs(null)} disabled={!mxYrs}
                          style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 6,
                                   cursor: mxYrs ? "pointer" : "default",
                                   border: `1px solid ${C.line}`, background: "#fff", color: mxYrs ? C.ink2 : C.muted }}>All</button>
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>
                    showing <b style={{ color: C.ink2 }}>{matrix.rows.length}</b> × <b style={{ color: C.ink2 }}>{matrix.intervals.length}</b>
                    {(mxIvs || mxYrs) ? " · this layout prints with the quote" : ""}
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 8, maxWidth: "82ch", lineHeight: 1.5 }}>
                  {view.note}
                  {isRain && matrix.rainfall_range && (
                    <> RMA publishes measured inches for <b style={{ color: C.ink2 }}>{matrix.rainfall_range.from}–{matrix.rainfall_range.to}</b> on this grid; earlier years are blank here and complete in the other views.</>
                  )}
                  {isRain && !matrix.rainfall_range && <> RMA publishes no measured inches for this grid.</>}
                </div>
                <div style={{ overflowX: "auto", maxHeight: 460, overflowY: "auto", border: `1px solid ${C.line}`, borderRadius: 6 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760, fontSize: 11.5 }}>
                    <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                      <tr>
                        <th style={{ textAlign: "left", padding: "6px", fontSize: 10.5, textTransform: "uppercase",
                                     letterSpacing: ".05em", color: C.muted, borderBottom: `1px solid ${C.line}` }}>Year</th>
                        {matrix.intervals.map((iv) => (
                          <th key={iv.interval} style={{ padding: "6px 4px", fontSize: 10, color: iv.elected ? C.ink : C.muted,
                                       fontWeight: iv.elected ? 800 : 600, borderBottom: `1px solid ${C.line}`,
                                       background: iv.elected ? "#f4f8f4" : "transparent", whiteSpace: "nowrap" }}>
                            {iv.elected && <span style={{ color: "#2e8b62" }}>●</span>} {iv.interval}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrix.rows.map((row) => (
                        <tr key={row.year} style={{ borderTop: `1px solid ${C.line}` }}>
                          <td style={{ padding: "4px 6px", fontWeight: 700, color: C.ink2, whiteSpace: "nowrap" }}>{row.year}</td>
                          {row.cells.map((c) => {
                            let text = "—", tint = 0, title = "";
                            if (view.id === "index" && c.index != null) {
                              text = (c.index * 100).toFixed(0) + "%";
                              tint = Math.max(0, Math.min(1, (1 - c.index) / 0.6));
                              title = c.interval + " " + row.year + " — RMA index " + (c.index * 100).toFixed(1) + "% of normal";
                            } else if (view.id === "factor" && c.factor != null) {
                              text = c.factor > 0 ? (c.factor * 100).toFixed(0) + "%" : "0";
                              tint = Math.max(0, Math.min(1, c.factor / 0.6));
                              title = c.interval + " " + row.year + " — pays " + (c.factor * 100).toFixed(1) + "% of protection";
                            } else if (view.id === "indemnity" && c.indemnity_per_acre != null) {
                              text = c.indemnity_per_acre > 0 ? "$" + c.indemnity_per_acre.toFixed(0) : "—";
                              tint = Math.max(0, Math.min(1, (c.factor || 0) / 0.6));
                              title = c.interval + " " + row.year + " — $" + c.indemnity_per_acre.toFixed(2) + "/ac at 100% of value in this interval";
                            } else if (view.id === "rainfall") {
                              if (c.rainfall_in != null) {
                                text = c.rainfall_in.toFixed(2);
                                tint = c.normal_in ? Math.max(0, Math.min(1, (1 - c.rainfall_in / c.normal_in) / 0.6)) : 0;
                                title = c.interval + " " + row.year + " — " + c.rainfall_in.toFixed(2) + " in against a published normal of " + c.normal_in.toFixed(2) + " in";
                              } else {
                                title = c.rainfall_unavailable_reason || "";
                              }
                            }
                            return (
                              <td key={c.interval} title={title}
                                  style={{ padding: "4px", textAlign: "center", whiteSpace: "nowrap",
                                           color: tint > 0.55 ? "#5c2f00" : C.ink2,
                                           background: tint > 0
                                             ? `rgba(201,139,75,${(0.08 + 0.55 * tint).toFixed(3)})`
                                             : (c.elected ? "#f8faf8" : "transparent"),
                                           borderLeft: c.elected ? `2px solid #cfe3d5` : "none" }}>
                                {text}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                    {/* ⚠ THE RATIO IS OVER THE YEARS ABOVE IT, AND NOTHING ELSE.
                        It comes from intervalHistory's replay_loss_ratio handed the
                        displayed year window, so narrowing the table narrows this by
                        construction. An interval RMA does not rate here prints "—",
                        never 0% — a zero would read as "this never paid", which is a
                        claim about rainfall rather than about a missing rate. */}
                    {matrixLR && (
                      <tfoot style={{ position: "sticky", bottom: 0, background: "#fff" }}>
                        <tr style={{ borderTop: `2px solid ${C.line}` }}>
                          <td style={{ padding: "6px", fontWeight: 800, color: C.ink, whiteSpace: "nowrap", fontSize: 10.5 }}>Loss ratio</td>
                          {matrix.intervals.map((iv) => {
                            const v = matrixLR[iv.interval];
                            return (
                              <td key={iv.interval}
                                  title={v == null
                                    ? iv.interval + " — no published rate at this coverage level, so no ratio can be formed"
                                    : iv.interval + " — " + Math.round(v * 100) + "% over " + matrix.rows.length + " displayed year" + (matrix.rows.length === 1 ? "" : "s") + ", on the producer premium rate"}
                                  style={{ padding: "5px 4px", textAlign: "center", whiteSpace: "nowrap",
                                           fontWeight: 800, fontSize: 11,
                                           background: iv.elected ? "#f4f8f4" : "transparent",
                                           color: v == null ? C.muted : v >= 1 ? "#2e8b62" : v >= 0.75 ? C.ink : C.muted }}>
                                {v == null ? "—" : Math.round(v * 100) + "%"}
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <div style={{ marginTop: 8, fontSize: 11.5, color: C.muted }}>
                  Shaded toward dry. <span style={{ color: "#2e8b62" }}>●</span> marks an interval this policy elected —
                  every other column is a counterfactual and was never insured.
                  {matrixLR && <> Loss ratio is the replay over the <b style={{ color: C.ink2 }}>{matrix.rows.length}</b> year{matrix.rows.length === 1 ? "" : "s"} shown:
                    mean payment factor over the producer premium rate, so it is comparable between intervals and does not move with the allocation.</>}
                </div>
              </div>
            );
          })()}
        </div>

        {!isPortal && (
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: C.muted, cursor: "pointer" }}>
            <input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} />
            Show the experimental prescriptive optimizer (agent only — measured unstable, not advice)
          </label>
        )}
      </div>
    );
  }

  // ── 4. THE RENEWAL QUOTE ──────────────────────────────────────────
  // Last year's structure is the STARTING POINT and every part of it is
  // editable. What it must never be is a silent default: the seed is stated on
  // screen, an edited field is marked, and clearing a box refuses the quote
  // rather than falling back to a figure nobody re-confirmed.
  function RenewalSection() {
    if (!renewal) return <div style={{ ...card, color: C.muted }}>Nothing to quote for this block.</div>;
    const r = renewal;
    const baseAlloc = r.alloc;
    const pcts = PRF_REVIEW.LAB.map((l) => Math.round((baseAlloc[l] || 0) * 100));
    const total = pcts.reduce((s, v) => s + v, 0);
    const setPct = (lab, v) => {
      const next = { ...baseAlloc };
      const val = Math.max(0, Math.min(100, Math.round(v))) / 100;
      if (val <= 0) delete next[lab]; else next[lab] = val;
      setSimAlloc(next);
    };
    // ── ALLOCATION SHORTCUTS — parity with Rain & Hail's Even / Odd / Clear ──
    // R&H prints its eleven boxes in two rows, and those rows are not cosmetic:
    // they are the only two families of intervals that can be elected together,
    // because neighbouring intervals share a month and RMA's Crop Provisions
    // (26-RI-PRF §2(b)) forbid insuring the same month twice. The families and
    // the split both come from the QUOTE ENGINE's own module, so a button can
    // never propose an election the engine would refuse.
    //
    // ⚠ THE SPLIT CAN REFUSE, AND THEN NOTHING IS APPLIED. The per-interval
    // minimum is 10% and the maximum is county-specific; six intervals do not
    // divide 100 evenly, so an even spread is 17/17/17/17/16/16 and a county
    // with a higher floor cannot take it. Silently writing an allocation an AIP
    // would refuse is worse than leaving the boxes alone.
    const applyFamily = (codes) => {
      const out = PRF_QUOTE_RULES.spreadEvenly(codes, r.limits || undefined);
      if (!out.alloc) { setAllocNote(out.reason); return; }
      setAllocNote(null);
      setSimAlloc(out.alloc);
    };
    const clearAlloc = () => { setAllocNote(null); setSimAlloc({}); };

    const edited = qAcres != null || qProd != null || simAlloc != null || simCov != null || qBfr != null;
    const inp = { padding: "6px 8px", border: `1px solid ${C.line}`, borderRadius: 6, fontSize: 14,
                  fontVariantNumeric: "tabular-nums", width: "100%" };
    const lab = { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 3 };
    const chip = (on) => ({ padding: "4px 10px", fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: "pointer",
      border: "1px solid " + (on ? "#2d6a4a" : C.line), background: on ? "#2d6a4a" : "#fff", color: on ? "#fff" : C.ink2 });
    const ERR = {
      NO_ACRES: "Enter the acres to quote — nothing is assumed.",
      NO_INTERVALS: "Elect at least two intervals.",
      CBV_NOT_SINGLE: "RMA publishes more than one county base value for this choice. Narrow it below.",
      CBV_DIFFERS_BY_INTERVAL: "This band's intervals carry different county base values, so it is not one product.",
      RATE_UNRESOLVED: "RMA publishes no single premium rate for these intervals at this choice.",
      QUOTE_REFUSED: "RMA would not accept this structure.",
      NO_RATE_RULE: "Rate rule unavailable.",
    };

    return (
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 14 }}>
        <div style={card}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
            <span style={{ ...lab, marginBottom: 0 }}>Quote year</span>
            {r.years.map((y) => (
              <button key={y} style={chip(r.year === y)} onClick={() => { setQYear(y); setQBand(null); }}>
                CY{y}{y === r.policyYear ? " · as written" : ""}
              </button>
            ))}
            {r.quoteUnavailable && (
              <span style={{ fontSize: 11.5, color: C.muted }}>
                RMA has published no CY{(r.policyYear || 0) + 1} rates for this grid yet.
              </span>
            )}
            <div style={{ flex: 1 }} />
            {edited && (
              <button onClick={() => { setQAcres(null); setQProd(null); setSimAlloc(null); setSimCov(null); setQBfr(null); }}
                      style={{ padding: "5px 10px", border: `1px solid ${C.line}`, borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 12 }}>
                Reset to last year
              </button>
            )}
            {/* Prints EVERY block on the policy, not just the one on screen —
                a quoting package for a two-grid policy that shows one grid is
                half a quote. */}
            <button onClick={printQuotePackage}
                    style={{ padding: "5px 11px", border: "1px solid #2d6a4a", borderRadius: 6,
                             background: "#2d6a4a", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
              Print quoting package
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 12, marginBottom: 12 }}>
            <div>
              <div style={lab}>Acres {qAcres != null && <b style={{ color: "#8a5a00" }}>edited</b>}</div>
              <input type="number" min="0" step="0.01" value={qAcres == null ? block.acres : qAcres}
                     onChange={(e) => setQAcres(e.target.value)} style={inp} />
            </div>
            <div>
              <div style={lab}>Coverage {simCov != null && <b style={{ color: "#8a5a00" }}>edited</b>}</div>
              <select value={r.cov} onChange={(e) => setSimCov(Number(e.target.value))} style={{ ...inp, background: "#fff" }}>
                {PRF_REVIEW.COVERAGE_LEVELS.map((c) => <option key={c} value={c}>{PC(c)}</option>)}
              </select>
            </div>
            <div>
              <div style={lab}>Productivity {qProd != null && <b style={{ color: "#8a5a00" }}>edited</b>}</div>
              <input type="number" min="0.6" max="1.5" step="0.01" value={qProd == null ? (r.prod ?? "") : qProd}
                     onChange={(e) => setQProd(e.target.value)} style={inp} />
            </div>
            <div>
              <div style={lab}>Allocated</div>
              <div style={{ ...inp, border: "1px solid transparent", padding: "6px 0", fontWeight: 700,
                            color: total === 100 ? C.ink : C.cost }}>
                {total}%{total === 100 ? "" : " — must total 100"}
              </div>
            </div>
          </div>

          {/* ⚠ BEGINNING FARMER AND RANCHER (MGR-25-006): +15 pts in BFR years 1-2,
              +13 in year 3, +11 in year 4, +10 in years 5-10. The policy year uses
              the points it was billed at; the renewal assumes one BFR year on and
              SAYS so, because only the agent knows the farmer's BFR year. */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ ...lab, marginBottom: 0 }}>Beginning farmer {qBfr != null && r.isNext && <b style={{ color: "#8a5a00" }}>edited</b>}</span>
            {r.isNext ? (
              <select value={qBfr == null ? "" : String(qBfr)} onChange={(e) => setQBfr(e.target.value === "" ? null : e.target.value)}
                      style={{ ...inp, width: "auto", background: "#fff" }}>
                <option value="">{data.bfrBilled?.bonus ? "As assumed from the bill" : "Not a BFR (as billed)"}</option>
                <option value="none">None</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((y) => (
                  <option key={y} value={y}>{`BFR year ${y} (+${Math.round(PRF_QUOTE_RULES.bfrBonusForYear(y) * 100)} pts)`}</option>
                ))}
              </select>
            ) : null}
            <span style={{ fontSize: 12, color: r.bfr?.assumed ? "#8a5a00" : C.muted }}>
              {r.bfr?.label || (data.bfrBilled == null && !r.isNext ? "BFR status not readable from the bill" : "Not a BFR")}
            </span>
            {Number(r.share) > 0 && Number(r.share) < 1 && (
              <span style={{ fontSize: 12, color: C.muted }}>
                · Share {Math.round(r.share * 1000) / 10}%{review?.block?.line_factor?.status === "DERIVED_SHARE" ? " (read from the AIP's liability)" : ""}
              </span>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 5 }}>
            <div style={{ ...lab, marginBottom: 0 }}>Intervals and percent of value</div>
            <div style={{ flex: 1 }} />
            <button onClick={() => applyFamily(PRF_QUOTE_RULES.IV_FAMILY_A)} style={chip(false)}
                    title="Jan-Feb, Mar-Apr, May-Jun, Jul-Aug, Sep-Oct, Nov-Dec — spread evenly">Even spread · 6</button>
            <button onClick={() => applyFamily(PRF_QUOTE_RULES.IV_FAMILY_B)} style={chip(false)}
                    title="Feb-Mar, Apr-May, Jun-Jul, Aug-Sep, Oct-Nov — spread evenly">Even spread · 5</button>
            <button onClick={clearAlloc} style={chip(false)} title="Clear every interval">Clear</button>
          </div>
          {allocNote && (
            <div style={{ fontSize: 11.5, color: "#8a5a00", marginBottom: 6 }}>{allocNote}</div>
          )}
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>
            The two rows below are the only two sets that can be elected together — neighbouring intervals share a month, and RMA does not insure the same month twice.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3,1fr)" : "repeat(6,1fr)", gap: 8 }}>
            {PRF_REVIEW.LAB.map((l, i) => (
              <label key={l} style={{ fontSize: 11, color: C.ink2 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: ivColor(l), display: "inline-block" }} />{l}
                </span>
                <input type="number" min="0" max="100" step="5" value={pcts[i]}
                       onChange={(e) => setPct(l, Number(e.target.value))}
                       style={{ ...inp, marginTop: 2, padding: "5px 6px", fontSize: 13 }} />
              </label>
            ))}
          </div>
          <div style={{ marginTop: 10 }}><AllocBar alloc={baseAlloc} /></div>

          {/* ⚠ CY2026 CARRIES NO PRACTICE CODE, SO THERE IS NOTHING TO INHERIT.
              The band moves the county base value — $330 against $447 on a real
              book grid — so it is asked for, never assumed. */}
          {r.bands.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.line}` }}>
              {/* SIX BANDS ARE TWO CHOICES. Within a tier the codes differ only
                  by organic status and price identically — measured, and
                  re-verified per render by practiceChoices, which refuses to
                  collapse a tier whose members disagree. The band code is RMA
                  plumbing and is off the face of the button. */}
              <div style={{ ...lab, marginBottom: 6 }}>
                Irrigation practice — it sets the county base value
                {!r.typeScoped && <b style={{ color: "#8a5a00" }}> · not narrowed to this block's use</b>}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                {(r.choices || []).map((c) => {
                  const on = c.families.includes(r.band)
                    || (r.band == null && r.carriedAgree && c.families.includes(r.carried[0]));
                  return (
                    <button key={c.label} style={chip(on)}
                            title={`RMA band ${c.families.map((f) => f + "xx").join(", ")}`}
                            onClick={() => setQBand(on && r.band != null ? null : c.families[0])}>
                      <b>{c.label}</b>{c.cbv != null ? ` · ${D(c.cbv)}/ac` : " · not published here"}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 7, lineHeight: 1.5, maxWidth: "76ch" }}>
                {r.band == null && r.carriedAgree
                  ? <>Carried from last year's <b>{D(r.lastCbv)}/ac</b>, solved from the AIP's own liability.
                     Pick the other practice to override.</>
                  : r.band == null
                    ? <span style={{ color: "#8a5a00" }}>Pick the practice on the grower's SOI — last year's
                      figure does not narrow it to one answer here.</span>
                    : <>Protection follows the county base value; the premium rate does not change with
                       practice.</>}
              </div>
            </div>
          )}
        </div>

        {/* ── the answer ─────────────────────────────────────────── */}
        {r.priced.ok ? (
          <div style={{ ...card, background: "#f7faf7", borderColor: "#cfe0cf" }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 14 }}>
              {[["Protection", D(r.priced.protection)], ["Producer premium", D(r.priced.producer_premium)],
                ["Protection / acre", D(r.priced.protection_per_acre)], ["Premium / acre", D(r.priced.premium_per_acre)]].map(([k, v]) => (
                <div key={k}>
                  <div style={lab}>{k}</div>
                  <div style={{ fontSize: isMobile ? 20 : 25, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums", lineHeight: 1.15 }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 11.5, color: C.muted }}>
              CY{r.year} rates · county base value {D(r.priced.cbv)}/ac · {Number(r.acres).toLocaleString("en-US")} acres
              {" · "}{PC(r.cov)} coverage · productivity {PC(r.prod)} · grid {block.grid}
              {edited ? " — edited from last year's policy" : " — last year's structure, unchanged"}
            </div>
          </div>
        ) : (
          <div style={{ ...card, borderColor: "#e8d9b8", background: "#fff8ec", color: "#8a5a00", fontSize: 13 }}>
            <b>No quote.</b> {ERR[r.priced.error] || r.priced.error}
            {r.priced.intervals ? ` (${r.priced.intervals.join(", ")})` : ""}
            {r.priced.message ? ` ${r.priced.message}` : ""}
          </div>
        )}

        {/* ── the one-page comparison ─────────────────────────────── */}
        {/* Comparing the policy year with itself, unedited, is a table of
            zeros under a heading that promises a change. Shown only when there
            is genuinely something on the other side. */}
        {r.compare && (r.year !== r.policyYear || edited) && (
          <div style={card}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
                What it cost last year, and what it costs now
              </div>
              <div style={{ fontSize: 11.5, color: C.muted }}>per acre is what compares</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, minWidth: 520 }}>
                <thead><tr>
                  <th style={th0}></th><th style={th}>CY{r.policyYear} as billed</th><th style={th}>CY{r.year} quoted</th><th style={th}>Change</th>
                </tr></thead>
                <tbody>
                  {r.compare.rows.map((row) => {
                    // Per-acre rows print cents; totals do not. See compareYears.
                    // The sign leads the currency symbol — "$-1.50" reads as a
                    // typo on a sheet an agent puts in front of a grower.
                    const f = (v) => {
                      if (v == null) return "—";
                      const n = Number(v), sign = n < 0 ? "−" : "";
                      const a = Math.abs(n);
                      if (row.cents) {
                        return sign + (row.unit === "$" ? "$" : "")
                          + a.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      }
                      return row.unit === "$" ? sign + "$" + Math.round(a).toLocaleString("en-US")
                                              : sign + a.toLocaleString("en-US");
                    };
                    return (
                      <tr key={row.label}>
                        <td style={td0}>{row.label}</td>
                        <td style={td}>{f(row.last)}</td>
                        <td style={td}>{f(row.next)}</td>
                        <td style={{ ...td, fontWeight: 600, color: !row.comparable ? C.muted : row.delta > 0 ? C.cost : row.delta < 0 ? C.gain : undefined }}>
                          {!row.comparable ? "not comparable" : row.delta == null ? "—"
                            : (row.delta > 0 ? "+" : "") + f(row.delta)
                              + (row.pct != null ? ` (${(row.pct * 100).toFixed(1)}%)` : "")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {r.compare.acres_changed && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#8a5a00", lineHeight: 1.5 }}>
                The acreage changed, so the TOTALS are not a like-for-like comparison — a bigger total
                premium on more acres is not a dearer product. The per-acre rows are the ones that answer
                "did PRF get cheaper".
              </div>
            )}
            <div style={{ marginTop: 12, fontSize: 12, color: C.ink2, lineHeight: 1.55, maxWidth: "72ch" }}>
              <b>Normal rainfall is not compared, and that is a measurement.</b> RMA's published normal
              barely moves between vintages — 0.002 in on APRMAY and 0.000 on the rest of this grid
              between the 2025 and 2026 windows — and RMA's decision-support API refuses a CY2027 sample
              window outright, so there is no published CY{r.nextYear || ""} normal to put beside it.
              What DID change is the basis: the CY2027 index history is <b>NCEI monthly</b> where CY2026
              is <b>CPC</b>. A normal from one and a normal from the other are not the same measurement.
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── 5. OPTIMIZER — experimental ───────────────────────────────────
  function OptimizerSection() {
    const o = review.optimizer;
    return (
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ ...card, borderColor: "#c9a227", background: "#fffdf4" }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#8a6d1f", marginBottom: 6 }}>
            ⚗ Experimental — not validated, not advice
          </div>
          <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.6, maxWidth: "74ch" }}>
            This engine selects the interval allocation with the most average net in the years this grid's rainfall
            <b> index</b> was lowest, subject to keeping a stated share of long-run economics and never covering fewer
            low-index years than the current contract. Three things are open, and until they close nothing here should
            reach a customer:
            <ul style={{ margin: "8px 0 0 18px" }}>
              <li><b>The answer depends on how far back you look, on every grid we insure.</b> Re-run over the
                  trailing 10, 15, 20, 25 and 30 years and the whole record — same grid, same coverage, same
                  intervals, only the window moving — and the selected allocation changed on <b>39 of 39</b> book
                  grids; one grid gave a different answer for all six windows. Worse, on <b>36 of 39</b> grids
                  <b> no</b> allocation is even Pareto-efficient in every window, against a median of 20 that are
                  efficient in at least one. So a single recommended allocation is false precision, and the window
                  — which the engine does not currently expose — is doing much of the choosing;</li>
              <li>severe years are defined from the same core index the allocation is optimised over — a circularity
                  risk that has not been ruled out;</li>
              <li>across the book it concentrates hard, and no current policy is shaped that way;</li>
              <li>whether that reflects real seasonal risk or an artifact of optimising one objective has not been
                  tested against other climates.</li>
            </ul>
            <div style={{ marginTop: 8 }}>
              The instability is measured, not a cause. The severe class is the bottom 20% of years, so a 10-year
              window rests on about <b>two</b> severe years — thin-sample noise and a genuinely changing rainfall
              regime would both look like this, and separating them has not been done.
            </div>
            <div style={{ marginTop: 8 }}>
              Low-index years are <b>not</b> measured forage losses. No crop outcome was observed.
            </div>
          </div>
        </div>

        {o.status === "NO_MATERIAL_IMPROVEMENT" && (
          <div style={card}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginBottom: 6 }}>Current coverage stands.</div>
            <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.6, maxWidth: "70ch" }}>
              No legal allocation improved the low-index-year result by more than <b>{D(o.materiality_bar_dollars)}</b>
              {" "}— 10% of this policy's annual producer premium — without covering fewer of those years. On the live
              book this is the answer for 32 of 61 policies. The tool declines rather than proposing a change for its
              own sake.
            </div>
          </div>
        )}

        {!isPortal && o.candidate && (
          <div style={card}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>Index-Severity Weighted <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".04em", color: "#8a6100", background: "#fdf7e8", border: "1px solid #e8d5a8", borderRadius: 5, padding: "1px 6px" }}>EXPERIMENTAL — allocation is unstable across historical windows</span></div>
              <div style={{ fontSize: 11.5, color: C.muted }}>
                retains {PC(o.candidate.pct_of_max_econ)} of maximum long-run economics
              </div>
            </div>
            <AllocBar alloc={o.candidate.allocation} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 22, marginTop: 12 }}>
              {[["Protection", D(o.candidate.protection)], ["Producer premium", D(o.candidate.producer_premium)],
                ["Low-index-year net", D(o.candidate.severe_avg_net)], ["Current low-index net", D(o.current_severe.avg_net)]].map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted }}>{l}</div>
                  <div style={{ fontSize: 17, fontWeight: 600, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{v}</div>
                </div>
              ))}
            </div>
            {o.candidate.relevance && !o.candidate.relevance.core_only && (
              <div style={{ marginTop: 10, fontSize: 12, color: C.cost }}>
                This structure leans on intervals outside the core forage season.
              </div>
            )}
          </div>
        )}

        {!isPortal && o.ladder && (
          <div style={card}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 8 }}>
              Economic-retention sensitivity — no production threshold has been chosen
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, minWidth: 520 }}>
                <thead><tr><th style={th0}>Retention floor</th><th style={th0}>Allocation</th>
                  <th style={th}>Low-index net</th><th style={th}>% of max economics</th></tr></thead>
                <tbody>
                  {o.ladder.map((l) => (
                    <tr key={l.retention} style={{ background: Math.abs(l.retention - o.retention_used) < 1e-9 ? "#eef4ee" : undefined }}>
                      <td style={td0}>{PC(l.retention)}</td>
                      <td style={{ ...td0, color: l.label ? C.ink : C.muted }}>{l.label || "no material improvement"}</td>
                      <td style={td}>{D(l.severe_avg_net)}</td>
                      <td style={td}>{l.pct_of_max_econ == null ? "—" : PC(l.pct_of_max_econ)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.55, maxWidth: "70ch" }}>
              The highlighted row is the provisional floor used above. Across the book the ladder changes the answer
              for only 11 of 61 policies, because the low-index-best allocation already retains a median 97% of
              maximum long-run economics — the two objectives are only weakly in conflict inside the core season.
            </div>
          </div>
        )}

        {!isPortal && (o.status === "RMA_LIMITS_UNVERIFIED" || o.status === "NO_SEVERITY" || o.status === "NO_UNIVERSE" || o.status === "CURRENT_UNPRICEABLE") && !o.candidate && (
          <div style={{ ...card, color: C.cost }}>
            No candidate produced: <b>{o.status}</b>.
            {o.status === "RMA_LIMITS_UNVERIFIED" && " RMA's percent-of-value limits are not loaded for this grid, so no allocation can be called legal."}
          </div>
        )}
      </div>
    );
  }
}

// ── NEW QUOTE — map first, and quotable with nothing saved ───────────────────
//
// The flow, in the order an agent actually works:
//   name the prospect -> find the ground -> draw it -> see the PHYSICAL grid
//   exposure -> ASSIGN the insured grid -> county / use -> coverage,
//   productivity, intervals -> price -> save, only if they want to.
//
// ⚠ NOTHING IS WRITTEN UNTIL SAVE. The whole draft is component state and the
// pricing runs through src/prf/prfQuoteDraft.js, which never touches the
// database. That is what makes "quote a prospect without creating a customer"
// true by construction rather than by remembering not to write.
//
// ⚠ THE INSURED GRID IS ASKED FOR, NEVER DERIVED. The map MEASURES where the
// ground lies and shows every grid it touches; assigning the block is a human
// act. 612 fields in this book straddle a grid line, and picking the larger
// half would put acres on an application nobody elected.
// ── COMPARE COVERAGE LEVELS, AND THE HISTORY UNDERNEATH THEM ─────────────────
// The AIP's own illustration prints 85% and 90% side by side and stops there.
// What it never answers is whether the extra premium bought anything, because
// answering that needs the grid's own record replayed at BOTH levels — coverage
// level is the TRIGGER, so a higher one pays in years a lower one does not.
//
// Everything here is deterministic and re-derivable: no model call, no score, no
// badge. It states what the levels cost and what this grid did, and stops. The
// arithmetic is the SAME engine as the quote above it — check-prf-compare ties
// the drawer to it year by year at zero tolerance, so the two cannot drift.
function PRFComparePanel({ draft, refs, isMobile }) {
  const C = { ink: "#1a2e1a", ink2: "#2d4a2d", line: "#d8e2d8", muted: "#6b7d6b", cost: "#9c580f", gain: "#166f49" };
  const card = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: isMobile ? 12 : 16, marginBottom: 14 };
  const lbl = { fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 6 };
  const [openHistory, setOpenHistory] = useState(false);
  const [lens, setLens] = useState("index");
  const [blockIdx, setBlockIdx] = useState(0);

  // Only blocks that are ready to price take part. A half-answered block must not
  // silently shrink the comparison — the totals card above already reports
  // excluded acreage, and this panel says how many blocks it could read.
  const ready = (draft.blocks || []).filter(
    (b) => PRF_DRAFT.assignmentReady(b) && refs[b.key]?.cbv != null && refs[b.key]?.rates_by_level);

  const rows = useMemo(() => {
    if (!ready.length) return [];
    return PRF_CMP.comparePolicy(ready.map((b) => ({
      label: b.label,
      countyBaseValue: refs[b.key].cbv,
      productivityFactor: draft.productivity_factor,
      acres: Number(b.acres) || 0,
      share: Number(b.share ?? 1),
      allocation: b.allocation || {},
      ratesByLevel: refs[b.key].rates_by_level,
      series: refs[b.key].series,
      rules: refs[b.key].limits || undefined,
    })), PRF_REVIEW.COVERAGE_LEVELS);
  }, [ready.map((b) => b.key + refs[b.key]?._key + JSON.stringify(b.allocation) + b.acres).join("|"),
      draft.productivity_factor]);

  const cur = rows.find((r) => Math.abs(r.coverage_level - draft.coverage_level) < 1e-9);
  const money = (n) => (n == null ? "—" : "$" + Math.round(n).toLocaleString());

  // The history drawer is per BLOCK, because the record belongs to a grid.
  const hb = ready[Math.min(blockIdx, Math.max(0, ready.length - 1))];
  const matrix = useMemo(() => {
    if (!openHistory || !hb) return null;
    const rf = refs[hb.key];
    if (!rf?.series || !Object.keys(rf.series).length) return null;
    const rates = rf.rates_by_level?.[draft.coverage_level];
    if (!rates) return null;
    let q = null;
    try {
      q = PRF_CMP.compareLevels({
        countyBaseValue: rf.cbv, productivityFactor: draft.productivity_factor,
        acres: Number(hb.acres) || 0, share: Number(hb.share ?? 1),
        allocation: hb.allocation || {}, ratesByLevel: rf.rates_by_level,
        series: rf.series, levels: [draft.coverage_level], rules: rf.limits || undefined,
      }).rows[0];
    } catch { return null; }
    if (!q?.priced) return null;
    // The ENGINE'S lines, never rebuilt here — one derivation of the interval
    // protection, so the drawer cannot drift from the quote printed above it.
    return PRF_CMP.historyMatrix({
      series: rf.series, normals: rf.normals, allocation: hb.allocation || {},
      coverageLevel: draft.coverage_level, quote: { lines: q.lines }, acres: Number(hb.acres) || 0,
    });
  }, [openHistory, hb?.key, refs[hb?.key]?._key, draft.coverage_level, draft.productivity_factor,
      JSON.stringify(hb?.allocation), hb?.acres]);

  if (!ready.length) return null;

  const th = { textAlign: "right", padding: "6px 8px", fontSize: 11, textTransform: "uppercase",
               letterSpacing: ".05em", color: C.muted, fontWeight: 700, whiteSpace: "nowrap" };
  const td = { textAlign: "right", padding: "7px 8px", fontSize: 13, color: C.ink,
               fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

  return (
    <div style={card}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: C.ink }}>Compare coverage levels</h3>
        <span style={{ fontSize: 12, color: C.muted }}>
          same acres, same intervals, same productivity factor — only the level changes
        </span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 620 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.line}` }}>
              <th style={{ ...th, textAlign: "left" }}>Level</th>
              <th style={th}>Protection</th>
              <th style={th}>Protection/ac</th>
              <th style={th}>Total premium</th>
              <th style={th}>Subsidy</th>
              <th style={th}>Producer premium</th>
              <th style={th}>vs current</th>
              <th style={th}>Paid in</th>
              <th style={th}>Avg net/yr</th>
              <th style={th}>Longest dry</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isCur = Math.abs(r.coverage_level - draft.coverage_level) < 1e-9;
              const bg = isCur ? "#f2f6f2" : "transparent";
              if (!r.priced) {
                return (
                  <tr key={r.coverage_level} style={{ background: bg, borderBottom: `1px solid ${C.line}` }}>
                    <td style={{ ...td, textAlign: "left", fontWeight: isCur ? 700 : 500 }}>
                      {Math.round(r.coverage_level * 100)}%{isCur ? "  ← current" : ""}
                    </td>
                    {/* ⚠ A LEVEL WE CANNOT PRICE IS SHOWN AND NAMED, NEVER DROPPED.
                        A row missing from this table reads as "RMA does not offer
                        that level here", which is a claim about the program.
                        ⚠ AND IT MUST NAME THE ACTUAL REASON. The first cut printed
                        "no published rate" for every unpriced level, so an
                        election REFUSED for overlapping intervals was reported as
                        missing actuarial data — sending an agent to look for a
                        gap in our tables over an election they can simply fix. A
                        confident wrong reason is worse than a vague right one. */}
                    <td style={{ ...td, textAlign: "left", color: C.cost }} colSpan={9}>
                      {(r.blocks_unpriceable || []).some((u) => u.reason === "REFUSED")
                        ? ((r.blocks_unpriceable || []).find((u) => u.reason === "REFUSED")?.message
                           || "This election cannot be quoted as entered.")
                        : <>No published rate for {(r.blocks_unpriceable || []).flatMap((u) => u.intervals || []).join(", ") || "an elected interval"}
                           {" "}— this level cannot be quoted from the actuarials we hold.</>}
                    </td>
                  </tr>
                );
              }
              const d = cur && cur.priced ? r.producer_premium - cur.producer_premium : null;
              return (
                <tr key={r.coverage_level} style={{ background: bg, borderBottom: `1px solid ${C.line}` }}>
                  <td style={{ ...td, textAlign: "left", fontWeight: isCur ? 700 : 500 }}>
                    {Math.round(r.coverage_level * 100)}%{isCur ? "  ← current" : ""}
                  </td>
                  <td style={td}>{money(r.protection)}</td>
                  {/* RMA's published per-acre figure, not total ÷ acres. */}
                  <td style={td}>{r.protection_per_acre == null ? "—" : "$" + r.protection_per_acre.toFixed(2)}</td>
                  <td style={td}>{money(r.total_premium)}</td>
                  <td style={td}>{money(r.subsidy)}<span style={{ color: C.muted, fontSize: 11 }}> {Math.round(r.subsidy_rate * 100)}%</span></td>
                  <td style={{ ...td, fontWeight: 700 }}>{money(r.producer_premium)}</td>
                  <td style={{ ...td, color: d === 0 || d == null ? C.muted : C.ink }}>
                    {d == null ? "—" : d === 0 ? "—" : (d > 0 ? "+" : "−") + money(Math.abs(d)).slice(1)}
                  </td>
                  <td style={td}>{r.history ? `${r.history.years_paid} of ${r.history.years}` : "—"}</td>
                  <td style={{ ...td, color: r.history ? (r.history.avg_net_per_year >= 0 ? C.ink : C.muted) : C.muted }}>
                    {r.history ? money(r.history.avg_net_per_year) : "—"}
                  </td>
                  <td style={td}>{r.history ? `${r.history.longest_dry_spell} yr` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.some((r) => r.priced && r.history) && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.6 }}>
          “Paid in”, “Avg net/yr” and “Longest dry” replay THIS election over the grid’s own published index —
          {" "}{rows.find((r) => r.history)?.history.years} years — at each level, charging that level’s own producer premium.
          {" "}Each level is re-run, never scaled: a higher level pays in years a lower one does not.
          {ready.length > 1 && <> Blocks are summed per year, so a year counts as paid when any block paid.</>}
        </div>
      )}

      {/* ── the trade, in sentences a person can check ── */}
      {(() => {
        const priced = rows.filter((r) => r.priced);
        if (priced.length < 2 || !cur?.priced) return null;
        const higher = priced.find((r) => r.coverage_level > draft.coverage_level);
        const said = higher ? PRF_CMP.explainTrade(cur, higher) : [];
        if (!said.length) return null;
        return (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
            <div style={lbl}>The trade</div>
            <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.75, maxWidth: "78ch" }}>
              {said.map((s, i) => <div key={i} style={{ marginBottom: 4 }}>{s}</div>)}
            </div>
          </div>
        );
      })()}

      {/* ── advanced history ── */}
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
        <button
          onClick={() => setOpenHistory((v) => !v)}
          style={{ fontSize: 12.5, fontWeight: 700, padding: "6px 11px", borderRadius: 7, cursor: "pointer",
                   border: `1px solid ${C.line}`, background: "#fff", color: C.ink2 }}
        >
          {openHistory ? "▾" : "▸"} Advanced history — every year, every interval
        </button>

        {openHistory && !matrix && (
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 10 }}>
            No published index history is loaded for this grid, so the matrix cannot be drawn.
            This says nothing about the grid’s rainfall — only that we hold no record of it here.
          </div>
        )}

        {openHistory && matrix && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 8 }}>
              {ready.length > 1 && (
                <select value={blockIdx} onChange={(e) => setBlockIdx(Number(e.target.value))}
                        style={{ fontSize: 12.5, padding: "6px 8px", borderRadius: 7, border: `1px solid ${C.line}` }}>
                  {ready.map((b, i) => <option key={b.key} value={i}>{b.label} — grid {b.insured_grid_id}</option>)}
                </select>
              )}
              {[["index", "Index %"], ["rainfall", "Rainfall (in)"],
                ["payment_factor", "Payment factor %"], ["indemnity_full", "Indemnity $/ac — all intervals"], ["indemnity_ac", "As elected only"]].map(([k, t]) => (
                <button key={k} onClick={() => setLens(k)}
                  style={{ fontSize: 12, padding: "5px 10px", borderRadius: 999, cursor: "pointer",
                           border: `1px solid ${lens === k ? "#2d4a2d" : C.line}`,
                           background: lens === k ? "#2d4a2d" : "#fff", color: lens === k ? "#fff" : C.ink2 }}>
                  {t}
                </button>
              ))}
              <span style={{ fontSize: 11.5, color: C.muted }}>
                grid {hb.insured_grid_id} · {matrix.rows.length} years · at {Math.round(draft.coverage_level * 100)}%
              </span>
            </div>

            <div style={{ overflowX: "auto", maxHeight: 460, overflowY: "auto", border: `1px solid ${C.line}`, borderRadius: 8 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 780 }}>
                <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                  <tr style={{ borderBottom: `1px solid ${C.line}` }}>
                    <th style={{ ...th, textAlign: "left" }}>Year</th>
                    {matrix.intervals.map((iv) => {
                      const on = (hb.allocation || {})[iv] > 0;
                      return (
                        <th key={iv} style={{ ...th, background: on ? "#eaf3ea" : "transparent", color: on ? C.ink : C.muted }}>
                          {/* ⚠ THIS FILE'S PRF_INTERVALS, NOT PRF_REVIEW's. Two
                              different things carry that name: the array here is
                              [{code,label,…}] with the display labels, while the
                              review module re-exports prfIndexMethodology's map
                              keyed 625..635 with no label at all. Reaching for
                              the wrong one crashed the whole page with
                              "PRF_INTERVALS.find is not a function" — and every
                              static check was green, because both names exist. */}
                          {PRF_INTERVALS.find((x) => x.code === iv)?.label || iv}
                          {on && <div style={{ fontSize: 10, fontWeight: 500 }}>{Math.round(hb.allocation[iv] * 100)}%</div>}
                        </th>
                      );
                    })}
                    <th style={{ ...th, borderLeft: `1px solid ${C.line}` }}>Year total<div style={{ fontSize: 10.5, fontWeight: 500, color: C.muted }}>as elected</div></th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((row) => (
                    <tr key={row.year} style={{ borderBottom: `1px solid ${C.line}` }}>
                      <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{row.year}</td>
                      {matrix.intervals.map((iv) => {
                        const c = row.cells[iv];
                        const on = (hb.allocation || {})[iv] > 0;
                        if (!c) return <td key={iv} style={{ ...td, color: C.muted }}>—</td>;
                        let v = "—", tint = "transparent";
                        if (lens === "index") v = (c.index * 100).toFixed(0);
                        else if (lens === "rainfall") v = c.rainfall == null ? "—" : c.rainfall.toFixed(2);
                        else if (lens === "indemnity_full") v = c.indemnity_ac_full == null ? "—" : c.indemnity_ac_full.toFixed(2);
                        else if (lens === "payment_factor") v = (c.payment_factor * 100).toFixed(0);
                        else v = on ? (c.indemnity_ac ? c.indemnity_ac.toFixed(2) : "0") : "—";
                        // A cell that would have TRIGGERED is tinted, on every
                        // lens — that is the question the drawer is opened to
                        // answer ("why did 2016 pay?"), and it must not depend on
                        // which lens happens to be selected.
                        if (c.payment_factor > 0) tint = on ? "#f3e8f7" : "#faf5fb";
                        return (
                          <td key={iv} style={{ ...td, background: tint, opacity: on ? 1 : 0.62,
                                                fontWeight: on && c.payment_factor > 0 ? 700 : 400 }}>{v}</td>
                        );
                      })}
                      <td style={{ ...td, borderLeft: `1px solid ${C.line}`, fontWeight: 700 }}>
                        {lens === "indemnity_ac"
                          ? (row.indemnity_ac == null ? "—" : "$" + row.indemnity_ac.toFixed(2))
                          : (row.indemnity == null ? "—" : "$" + Math.round(row.indemnity).toLocaleString())}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.65, maxWidth: "86ch" }}>
              Elected intervals are shaded in the header; a tinted cell is an interval whose index fell below
              {" "}{Math.round(draft.coverage_level * 100)}%, so RMA would have paid on it.
              {" "}<b>Payment factor</b> is allocation-independent — it shows which intervals have historically paid on this
              grid, including ones this quote does not elect. <b>Indemnity $/ac</b> is this election only, so an
              un-elected interval reads “—” because the grower would have received nothing there.
              <b>If elected 100%</b> shows what EVERY interval would have paid per acre at the full election — a different basis, so those cells do not sum to the year total.
              {lens === "rainfall" && (
                <> {" "}<b style={{ color: C.cost }}>Rainfall here is RECONSTRUCTED</b> as index × RMA’s published normal for
                  the interval, not a gauge reading — the index is the number RMA settles on, and the inches are shown
                  to make it legible.</>
              )}
              {" "}The index is rainfall: past intervals carry no claim on future ones.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PRFNewQuoteView({ farmers, isMobile, seed, onSaved }) {
  // ── WHICH CROP YEAR A NEW QUOTE IS FOR ─────────────────────────────────
  //
  // Kevin, 2026-09-03: "New Quote in PRF needs to be CY2027, its currently
  // 2026." It was a hardcoded 2026, which was right when it was written and
  // silently wrong the day RMA published the next year.
  //
  // ⚠ SO IT IS NOT HARDCODED TO 2027 EITHER — that just moves the staleness
  // one season. RMA publishes premium rates for a crop year when it can be
  // sold, so the newest year in `prf_premium_rates` IS the answer, and it
  // advances on its own the day the ADM pull lands.
  //
  // The constant below is the FALLBACK and the cross-check, never the source —
  // the same shape as the RMA price ladder. A probe that fails leaves the
  // constant in charge AND says the year could not be confirmed, because a
  // quote is a document a grower signs and the year on it is not a detail.
  // Measured 2026-09-03: CY2027 rates, county base values and interval limits
  // are complete on all 39 book grids, and the CY2027 index-history vintage
  // exists, so the history a quote draws does not empty out.
  const CROP_YEAR_FALLBACK = 2027;
  const [pubYear, setPubYear] = useState(null);      // { year, source } once known
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const rows = await sbFetch("prf_premium_rates", {
          select: "crop_year", order: "crop_year.desc", limit: 1,
        });
        const y = Number(rows?.[0]?.crop_year);
        if (!dead && Number.isFinite(y) && y >= 2000 && y <= 2100) {
          setPubYear({ year: y, source: "published" });
          return;
        }
      } catch (_) { /* fall through to the constant, and say so */ }
      if (!dead) setPubYear({ year: CROP_YEAR_FALLBACK, source: "unconfirmed" });
    })();
    return () => { dead = true; };
  }, []);
  const CROP_YEAR = pubYear ? pubYear.year : CROP_YEAR_FALLBACK;
  const C = { ink: "#1a2e1a", ink2: "#2d4a2d", line: "#d8e2d8", muted: "#6b7d6b", cost: "#9c580f", gain: "#166f49" };
  const card = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: isMobile ? 12 : 16, marginBottom: 14 };
  const lbl = { fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 6 };
  const inp = { fontSize: 13, padding: "7px 9px", borderRadius: 7, border: `1px solid ${C.line}`, background: "#fff", color: C.ink };
  const btn = (primary) => ({ fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 8, cursor: "pointer",
    border: "1px solid " + (primary ? "#2d4a2d" : C.line), background: primary ? "#2d4a2d" : "#fff", color: primary ? "#fff" : C.ink2 });

  const [draft, setDraft] = useState(() => {
    const d = PRF_DRAFT.newDraft({ cropYear: CROP_YEAR, stateCode: "36" });
    if (seed?.coverage_level) d.coverage_level = seed.coverage_level;
    return d;
  });
  // The draft is created before the probe answers, so it takes the resolved
  // year the moment there is one. Nothing has been priced yet at that point —
  // a fresh draft carries no blocks — so this cannot move a figure already on
  // screen; it sets the year the first block will be priced at.
  useEffect(() => {
    if (!pubYear) return;
    setDraft((d) => (d.crop_year === pubYear.year ? d : { ...d, crop_year: pubYear.year }));
  }, [pubYear]);

  const [addr, setAddr] = useState("");
  const [geo, setGeo] = useState(null);        // { matches, error }
  const [geoBusy, setGeoBusy] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [pts, setPts] = useState([]);          // [[lon,lat], …] while drawing
  // Which PRF cell the map centre sits in — the grid readout that stays useful
  // at field zoom, where the cell's own edges are miles off screen.
  const [centreGrid, setCentreGrid] = useState(null);
  const [refs, setRefs] = useState({});        // blockKey -> { cbv, rates, limits, … }
  const [saveState, setSaveState] = useState(null);
  const mapRef = useRef(null), elRef = useRef(null), layersRef = useRef({});

  const patch = (fn) => setDraft((d) => { const n = JSON.parse(JSON.stringify(d)); fn(n); return n; });

  // ── the map ──────────────────────────────────────────────────────
  useEffect(() => {
    let dead = false;
    loadLeaflet().then((L) => {
      if (dead || !elRef.current || mapRef.current) return;
      const seedC = seed?.grid ? PRF_GRID.prfGridCentre(seed.grid) : null;
      const m = L.map(elRef.current, { scrollWheelZoom: true })
        .setView(seedC ? [seedC.lat, seedC.lon] : [43.0, -77.7], seedC ? 11 : 8);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
        attribution: "Esri", maxZoom: 18,
      }).addTo(m);
      // ⚠ BARE IMAGERY HAS NO ROAD NAMES, AND THAT IS HOW GROUND GETS FOUND.
      // An agent locating a prospect's field navigates by "the corner of Two Rod
      // and Sandridge", not by squinting at tree lines — and a town centroid
      // (all a ZIP or place search can give) is only useful if you can then read
      // your way to the right block from it. These are Esri's own reference
      // layers, the same pair its "Imagery Hybrid" basemap uses: transportation
      // first, then boundaries and place labels on top.
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 18, opacity: 0.9, pane: "overlayPane",
      }).addTo(m);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 18, pane: "overlayPane",
      }).addTo(m);
      layersRef.current.grid = L.layerGroup().addTo(m);
      layersRef.current.draw = L.layerGroup().addTo(m);
      m.on("click", (e) => {
        if (!drawRef.current) return;
        setPts((p) => [...p, [e.latlng.lng, e.latlng.lat]]);
      });
      mapRef.current = m;
      setTimeout(() => m.invalidateSize(), 60);
    }).catch(() => {});
    return () => { dead = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  // `drawing` read inside a Leaflet handler registered once — a ref, or the
  // handler closes over the first value forever.
  const drawRef = useRef(false);
  useEffect(() => { drawRef.current = drawing; }, [drawing]);

  // The PRF lattice over whatever is on screen, so the grid a block will be
  // assigned to is visible while the ground is being drawn.
  useEffect(() => {
    const m = mapRef.current, L = window.L;
    if (!m || !L) return;
    const redraw = () => {
      const g = layersRef.current.grid; if (!g) return;
      // ONE definition of the lattice — see prfDrawLattice. It returns the cell
      // the map centre is in, which is what keeps the grid legible at field zoom.
      setCentreGrid(prfDrawLattice(L, m, g));
    };
    redraw();
    m.on("moveend zoomend", redraw);
    return () => { m.off("moveend zoomend", redraw); };
  }, [draft.blocks.length]);

  // The polygon being drawn.
  useEffect(() => {
    const L = window.L, g = layersRef.current.draw;
    if (!L || !g) return;
    g.clearLayers();
    if (pts.length) {
      L.polygon(pts.map((p) => [p[1], p[0]]), { color: "#e0a03a", weight: 2, fillOpacity: 0.25 }).addTo(g);
      pts.forEach((p) => L.circleMarker([p[1], p[0]], { radius: 4, color: "#e0a03a", fillOpacity: 1 }).addTo(g));
    }
    for (const b of draft.blocks) {
      if (!b.geom) continue;
      L.polygon(b.geom.coordinates[0].map((p) => [p[1], p[0]]), { color: "#2f6e2f", weight: 2, fillOpacity: 0.25 }).addTo(g);
    }
  }, [pts, draft.blocks]);

  // ── address ──────────────────────────────────────────────────────
  const findAddress = async () => {
    const a = addr.trim(); if (!a) return;
    setGeoBusy(true); setGeo(null);
    try {
      const r = await fetch(`/api/market?fn=prf-geocode&address=${encodeURIComponent(a)}`, { headers: getApiAuthHeaders() });
      const j = await r.json();
      if (!j.ok) { setGeo({ error: j.error || "The address lookup failed." }); return; }
      setGeo({ matches: j.matches });
      // Zoom to the PRECISION of the match, not to a fixed level. A rooftop
      // earns 14; a ZIP or a town centroid gets 12, because zooming to a field's
      // worth of detail around a centroid nobody typed invites drawing there.
      if (j.matches[0] && mapRef.current) {
        const p = j.matches[0].precision;
        mapRef.current.setView([j.matches[0].lat, j.matches[0].lon], p && p !== "address" ? 12 : 14);
      }
    } catch (e) { setGeo({ error: "The address lookup could not be reached." }); }
    finally { setGeoBusy(false); }
  };

  // ── finishing a drawing = MEASURING, never assigning ─────────────
  const finishDraw = () => {
    if (pts.length < 3) return;
    const ring = [...pts, pts[0]];
    const geom = { type: "Polygon", coordinates: [ring] };
    const exp = PRF_GRID.gridExposure(geom);
    if (!exp) { setDrawing(false); setPts([]); return; }
    const b = PRF_DRAFT.newBlock({
      label: `Block ${draft.blocks.length + 1}`,
      acres: Math.round(exp.total_acres * 100) / 100, exposure: exp,
    });
    b.geom = geom;
    // Seeded from the Grid Explorer only when the drawn ground actually touches
    // that grid — a handover must not assign acres that are somewhere else.
    if (seed?.grid && exp.grids.some((g) => g.grid === seed.grid)) b.insured_grid_id = seed.grid;
    patch((d) => { d.blocks.push(b); });
    setDrawing(false); setPts([]);
  };

  // A BLOCK WITH NO GROUND DRAWN. Everything downstream already supports it —
  // `newBlock` takes exposure: null, `assignmentReady` asks only for grid, county
  // and use, and the per-block refs fetch keys on insured_grid_id. This is the path
  // for a prospect on the phone: a grid number, a county, acres, and a price.
  const addTypedBlock = () => {
    const b = PRF_DRAFT.newBlock({ label: `Block ${draft.blocks.length + 1}`, acres: null, exposure: null });
    // A grid handed over by the Grid Explorer is applied here WITHOUT a polygon to
    // check it against — which is safe precisely because there is no measurement to
    // contradict. The drawn path keeps its guard, where an exposure does exist.
    if (seed?.grid) b.insured_grid_id = Number(seed.grid);
    patch((d) => { d.blocks.push(b); });
  };

  // ── reference data per block, on its OWN identity ────────────────
  useEffect(() => {
    let dead = false;
    (async () => {
      for (const b of draft.blocks) {
        if (!PRF_DRAFT.assignmentReady(b)) continue;
        // ⚠ THE PRACTICE IS PART OF THIS KEY. Without it, choosing a practice on
        // a cell whose county base value VARIES by practice left the cached refs
        // untouched — the effect skipped, `cbv` stayed null, and the block went
        // on refusing with "choose the practice above" while the practice sat
        // chosen in the dropdown beside it. The identity that decides the price
        // is the identity the cache must be keyed on; anything less is a stale
        // value that looks like a bug in the data.
        const key = `${b.insured_grid_id}|${b.county_code}|${b.intended_use}|${b.practice_code || ""}|${draft.coverage_level}`;
        if (refs[b.key]?._key === key) continue;
        const typeCode = b.intended_use === "GRAZING" ? "007" : "030";
        const [rateRows, cbvRows, limitRows, idxRows, normRows] = await Promise.all([
          sbFetch("prf_premium_rates", {
            select: "interval_code,coverage_level,base_rate,type_code,county_code,practice_code",
            filters: [`grid_id=eq.${b.insured_grid_id}`, `crop_year=eq.${draft.crop_year}`],
          }),
          sbFetch("prf_county_base_values", {
            // interval_code rides along so the practice picker can NAME the tier:
            // RMA folds the interval into the practice code (band 525 + MAYJUN = 529),
            // and the band is what says irrigated or not.
            select: "crop_year,state_code,county_code,grid_id,type_code,practice_code,interval_code,county_base_value",
            filters: [`grid_id=eq.${b.insured_grid_id}`, `crop_year=eq.${draft.crop_year}`], maxPages: 3,
          }),
          sbFetch("prf_interval_limits", {
            select: "grid_id,crop_year,interval_code,min_percent,max_percent",
            filters: [`grid_id=eq.${b.insured_grid_id}`, `crop_year=eq.${draft.crop_year}`], limit: 50,
          }),
          // The grid's own rainfall record — 77 years x 11 intervals, ~850 rows.
          // `source_crop_year` matters: RMA RESTATES published history every year
          // as the normal's window advances, so a vintage must be pinned or the
          // drawer mixes two of them. interval_code arrives NUMERIC (625..635)
          // here and is mapped by indexSeries, not by a second local table.
          sbFetch("prf_index_history", {
            select: "year,interval_code,index_value",
            filters: [`grid_id=eq.${b.insured_grid_id}`, `source_crop_year=eq.${draft.crop_year}`],
            order: "year.asc", maxPages: 5,
          }).catch(() => []),
          sbFetch("prf_grid_normals", {
            select: "interval_code,normal_inches,methodology",
            filters: [`grid_id=eq.${b.insured_grid_id}`,
                      `methodology=eq.${prfNormalMethodology(draft.crop_year)}`], limit: 50,
          }).catch(() => []),
        ]);
        if (dead) return;
        const mine = cbvRows.filter((r) => String(r.county_code) === String(b.county_code) && String(r.type_code) === typeCode);
        // ⚠ A COUNTY CODE IS NOT UNIQUE ACROSS STATES, AND A PRF GRID STRADDLES
        // STATE LINES — 47 of 1,104 grid memberships do. This filter keys on
        // county_code and type only, so on a border grid it can return TWO
        // STATES' rows, and the practice codes are identical in both. That makes
        // a state collision indistinguishable from a practice ambiguity, and a
        // practice chosen to resolve it can silently return the OTHER state's
        // value. Measured 2026-09-01 on the CY2027 actuarial: grids 26613 and
        // 26614, county 015, publish under BOTH 36 (Chemung NY) and 42
        // (Bradford PA) — grazing $49.50 vs $49.80, haying $236 / $261 / $314 /
        // $348. **No grid in the book is affected (0 of 39), so this is latent
        // rather than live** — but the block carries no state today, so the
        // honest answer is to REFUSE by name rather than pick. Give the block a
        // state and this becomes an ordinary filter.
        const cbvStates = [...new Set(mine.map((r) => String(r.state_code)))];
        const vals = [...new Set(mine.map((r) => Number(r.county_base_value)))];
        // `tier` is "Irrigated" / "Non-irrigated" where the band is one RMA's own
        // pricing tool has been probed for (prfRenewalQuote BAND_PRACTICE), null
        // otherwise — the same table the renewal quote labels its picker from, so
        // the two quoters cannot name a band differently.
        const byPractice = mine.map((r) => ({ practice_code: String(r.practice_code), cbv: Number(r.county_base_value),
                                              tier: PRF_RQ.bandLabel(PRF_RQ.familyOf(r.practice_code, r.interval_code)) }))
          .sort((x, y) => x.cbv - y.cbv);
        setRefs((m) => ({ ...m, [b.key]: {
          _key: key,
          // ⚠ ONE VALUE OR NOTHING. Where the practices disagree the quote does
          // NOT pick — the producer's own practice decides, and averaging two
          // published values would invent a third RMA never printed.
          cbv: cbvStates.length > 1 ? null
             : vals.length === 1 ? vals[0] : (b.practice_code ? byPractice.find((p) => p.practice_code === String(b.practice_code))?.cbv ?? null : null),
          cbv_choices: byPractice, cbv_varies: vals.length > 1,
          cbv_states: cbvStates, cbv_multi_state: cbvStates.length > 1,
          // ⚠ A RATE ROW'S county_code IS NOT A DIMENSION — HAND IT TO ratesFor,
          // NEVER PRE-FILTER ON IT. RMA prices plan 13 per GRID, and a grid is a
          // 0.25° box that straddles counties: grid 27509's 165 CY2026 rows carry
          // county 121 ×49, 055 ×48, 037 ×36 and 051 ×32, whichever offer the ADM
          // loader saw last. A hard filter keeps 32 of 165 and has no fallback, so
          // it reports "no base rate" for rates RMA publishes. Measured 2026-08-25
          // on Livingston/haying: 5 of 11 intervals priced at 85% and 3 of 11 at
          // 90% — including BOTH intervals of the Rain and Hail illustration
          // check-prf-golden replays, so its whole 90% column was unquotable.
          // ratesFor narrows SOFTLY (it keeps the wider set when a narrow empties
          // it), which is exactly the fallback a pre-filter throws away.
          rates: PRF_REVIEW.ratesFor(rateRows, draft.coverage_level, { typeCode, countyCode: b.county_code }),
          // EVERY level, off the same rows — the fetch is already unfiltered by
          // coverage, so comparing levels costs one in-memory pass rather than
          // five round trips, and the compare table can never read a rate the
          // quote above it did not.
          rates_by_level: Object.fromEntries(PRF_REVIEW.COVERAGE_LEVELS.map((c) =>
            [c, PRF_REVIEW.ratesFor(rateRows, c, { typeCode, countyCode: b.county_code })])),
          series: PRF_REVIEW.indexSeries(idxRows || []).series,
          history_years: PRF_REVIEW.indexSeries(idxRows || []).years,
          normals: Object.fromEntries((normRows || []).map((r) => {
            const c = Number(r.interval_code);
            return [Number.isFinite(c) ? PRF_CMP.LAB[c - 625] : String(r.interval_code), Number(r.normal_inches)];
          }).filter(([l]) => l)),
          limits: PRF_REVIEW.limitsFor(limitRows, b.insured_grid_id, draft.crop_year),
          counties: [...new Set(cbvRows.map((r) => String(r.county_code)))].sort(),
        } }));
      }
    })();
    return () => { dead = true; };
  }, [draft.blocks, draft.coverage_level, draft.crop_year]);

  // Counties a freshly-assigned grid offers, before any block ref exists.
  const [gridCounties, setGridCounties] = useState({});
  useEffect(() => {
    let dead = false;
    (async () => {
      for (const b of draft.blocks) {
        if (b.insured_grid_id == null || gridCounties[b.insured_grid_id]) continue;
        const rows = await sbFetch("prf_county_base_values", {
          select: "county_code,type_code,grid_id",
          filters: [`grid_id=eq.${b.insured_grid_id}`, `crop_year=eq.${draft.crop_year}`], maxPages: 3,
        });
        if (dead) return;
        setGridCounties((m) => ({ ...m, [b.insured_grid_id]: [...new Set(rows.map((r) => String(r.county_code)))].sort() }));
      }
    })();
    return () => { dead = true; };
  }, [draft.blocks, draft.crop_year]);

  const pricing = useMemo(() => PRF_DRAFT.priceDraft(draft, refs), [draft, refs]);
  const gaps = useMemo(() => PRF_DRAFT.draftGaps(draft), [draft]);

  // ── save, only when asked ────────────────────────────────────────
  const save = async (presented) => {
    setSaveState({ busy: true });
    try {
      const rows = PRF_DRAFT.draftToRows(draft, pricing, { admVintage: `CY${draft.crop_year} ADM`, presented });
      const [q] = await sbInsert("prf_quotes", [rows.quote]);
      if (!q?.id) throw new Error("the quote row did not come back");
      const blockRows = rows.blocks.map(({ _key, ...r }) => ({ ...r, quote_id: q.id }));
      const saved = await sbInsert("prf_quote_blocks", blockRows);
      const idFor = {};
      rows.blocks.forEach((b, i) => { idFor[b._key] = saved[i]?.id; });
      const expRows = rows.exposure.map(({ _key, ...r }) => ({ ...r, block_id: idFor[_key] })).filter((r) => r.block_id);
      const ivRows = rows.intervals.map(({ _key, ...r }) => ({ ...r, block_id: idFor[_key] })).filter((r) => r.block_id);
      if (expRows.length) await sbInsert("prf_quote_block_exposure", expRows);
      if (ivRows.length) await sbInsert("prf_quote_intervals", ivRows);
      setSaveState({ ok: true, id: q.id, presented });
      if (onSaved) onSaved(q.id);
    } catch (e) {
      setSaveState({ error: e?.message || String(e) });
    }
  };

  const Stat = ({ label, value, sub }) => (
    <div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: C.ink }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: C.muted }}>{sub}</div> : null}
    </div>
  );
  const prfFarmers = (farmers || []).filter((f) => (f.prfPolicies || []).length);

  return (
    <div>
      {/* ── who ── */}
      <div style={card}>
        <div style={lbl}>Who is this for</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <input placeholder="Prospect name" value={draft.subject.prospect_name}
                 onChange={(e) => patch((d) => { d.subject.prospect_name = e.target.value; d.subject.farmer_id = null; })}
                 style={{ ...inp, minWidth: 240 }} />
          <span style={{ fontSize: 12, color: C.muted }}>or</span>
          <select value={draft.subject.farmer_id || ""}
                  onChange={(e) => patch((d) => { d.subject.farmer_id = e.target.value || null; if (e.target.value) d.subject.prospect_name = ""; })}
                  style={{ ...inp, minWidth: 240 }}>
            <option value="">— attach to an existing customer —</option>
            {(farmers || []).slice().sort((a, b) => String(a.name).localeCompare(String(b.name)))
              .map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8, maxWidth: "80ch", lineHeight: 1.6 }}>
          A prospect needs no customer record and nothing is written until you save. {prfFarmers.length} of your
          customers already carry PRF.
        </div>
      </div>

      {/* ── where ── */}
      <div style={card}>
        <div style={lbl}>Find the ground</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <input placeholder="Address, town, or intersection" value={addr}
                 onChange={(e) => setAddr(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") findAddress(); }}
                 style={{ ...inp, minWidth: 320, flex: "1 1 320px" }} />
          <button onClick={findAddress} disabled={geoBusy} style={btn(false)}>{geoBusy ? "Searching…" : "Find"}</button>
          {!drawing && <button onClick={() => { setDrawing(true); setPts([]); }} style={btn(true)}>Draw the field</button>}
          {/* The prospect path. Drawing MEASURES the ground and is the better
              answer when the ground is known; this is for the call where it is
              not — a grid, a county, an acreage, a price. Nothing here derives
              a grid, so the election stays a human act either way. */}
          {!drawing && <button onClick={addTypedBlock} style={btn(false)}>Add a block without the map</button>}
          {drawing && <>
            <button onClick={finishDraw} disabled={pts.length < 3} style={btn(true)}>Finish ({pts.length} points)</button>
            <button onClick={() => { setDrawing(false); setPts([]); }} style={btn(false)}>Cancel</button>
          </>}
        </div>
        {geo?.error && <div style={{ fontSize: 12.5, color: C.cost, marginBottom: 8 }}>{geo.error}</div>}
        {geo?.matches && geo.matches.length === 0 && (
          <div style={{ fontSize: 12.5, color: C.cost, marginBottom: 8 }}>
            No match — not as an address, a ZIP or a town. The map has not moved; pan to the ground instead.
          </div>
        )}
        {geo?.matches?.length > 0 && (
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 8 }}>
            {/* ⚠ A TOWN CENTROID IS NOT THE GROWER'S GROUND. The Census address
                geocoder only matches a house number and street, so a town or a
                ZIP falls through to TIGERweb — good enough to put the map in the
                right neighbourhood, and NOT a location anybody typed. Saying
                which kind of match this was is what stops an agent drawing a
                block against a centroid and landing in the wrong grid. */}
            {geo.matches[0].precision === "address"
              ? <>Matched <b style={{ color: C.ink }}>{geo.matches[0].address}</b></>
              : <>Centred on <b style={{ color: C.ink }}>{geo.matches[0].address}</b>
                  {" "}<span style={{ color: C.cost }}>
                    — {geo.matches[0].precision === "zip" ? "ZIP-code area" : "town centre"}, not a street address.
                    Pan and zoom to the actual ground before drawing.
                  </span></>}
            {geo.matches.length > 1 && (
              <>
                {" "}·{" "}
                {geo.matches.slice(1, 4).map((m, k) => (
                  <button key={k}
                    onClick={() => mapRef.current && mapRef.current.setView([m.lat, m.lon], m.precision === "address" ? 15 : 13)}
                    style={{ fontSize: 11.5, padding: "2px 7px", marginLeft: 4, borderRadius: 6,
                             border: `1px solid ${C.line}`, background: "#fff", color: C.ink2, cursor: "pointer" }}>
                    {m.address}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
        {/* The grid readout. It is here rather than only on the map because a
            PRF cell is ~17 miles across: at the zoom you draw a field at, its
            edges are off screen and the lattice looks absent. */}
        {centreGrid != null && (
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 8 }}>
            Centre of the map is in PRF grid <b style={{ color: C.ink }}>{centreGrid}</b>
            {" "}<span style={{ color: C.muted }}>
              — a grid is 0.25° (about 17 miles), so its blue edges only appear when one crosses the view.
              Zoom out to see the lattice.
            </span>
          </div>
        )}
        {drawing && (
          <div style={{ fontSize: 12.5, color: C.ink2, marginBottom: 8 }}>
            Click the corners of the field on the map, then Finish. The blue boxes are RMA's PRF grids.
          </div>
        )}
        <div ref={elRef} style={{ height: isMobile ? 320 : 460, borderRadius: 8, border: `1px solid ${C.line}`, cursor: drawing ? "crosshair" : "grab" }} />
      </div>

      {/* ── blocks ── */}
      {draft.blocks.map((b, i) => {
        const r = refs[b.key] || {};
        const counties = gridCounties[b.insured_grid_id] || r.counties || [];
        const p = pricing.blocks.find((x) => x.key === b.key);
        const alloc = PRF_DRAFT.allocationTotal(b);
        return (
          <div key={b.key} style={card}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <input value={b.label} onChange={(e) => patch((d) => { d.blocks[i].label = e.target.value; })} style={{ ...inp, width: 180, fontWeight: 700 }} />
              {/* ⚠ MEASURED ACRES ARE NOT EDITED; TYPED ACRES ARE. A drawn block's
                  acreage is what the polygon measures and stays read-only, or the
                  number under "MEASURED" would stop being a measurement. A block with
                  no ground drawn has nothing to measure, so it is typed — which is
                  what blockGaps has always told the reader they could do. */}
              {b.exposure ? (
                <span style={{ fontSize: 13, color: C.ink }}>
                  <b>{Number(b.acres).toLocaleString(undefined, { maximumFractionDigits: 2 })}</b> ac
                </span>
              ) : (
                <span style={{ fontSize: 13, color: C.ink, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <input type="number" min="0" step="1" value={b.acres ?? ""} placeholder="acres"
                         onChange={(e) => patch((d) => { d.blocks[i].acres = e.target.value === "" ? null : Number(e.target.value); })}
                         style={{ ...inp, width: 96 }} /> ac
                </span>
              )}
              <button onClick={() => patch((d) => { d.blocks.splice(i, 1); })} style={{ ...btn(false), marginLeft: "auto" }}>Remove</button>
            </div>

            {/* ── MEASURED ────────────────────────────────────────────
                The two halves are given DIFFERENT SHAPES on purpose. This is
                the concept an agent is most likely to conflate — "where the
                land is" versus "which grid you are electing" — and when both
                were plain rows of the same weight the screen implied the first
                simply became the second. Measurement sits on a tinted panel
                labelled MEASURED and carries no controls; the election sits on
                white, is labelled YOUR ELECTION, and is nothing but controls. */}
            <div style={{ background: "#f4f8f4", border: `1px solid ${C.line}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ ...lbl, marginBottom: 4 }}>
                <span style={{ background: "#dbe7db", color: C.ink2, padding: "1px 6px", borderRadius: 4, marginRight: 8 }}>Measured</span>
                Physical grid exposure — where these acres lie
              </div>
              <div style={{ fontSize: 13 }}>
              {(b.exposure?.grids || []).map((g) => (
                <span key={g.grid} style={{ display: "inline-block", marginRight: 14 }}>
                  Grid <b>{g.grid}</b> — {g.acres.toLocaleString(undefined, { maximumFractionDigits: 2 })} ac
                  <span style={{ color: C.muted }}> ({Math.round(g.share * 100)}%)</span>
                </span>
              ))}
              <div style={{ fontSize: 12, color: b.exposure?.straddles ? C.cost : C.muted, marginTop: 4, maxWidth: "80ch", lineHeight: 1.6 }}>
                {b.exposure?.straddles
                  ? "These acres cross a grid line. They are NOT split automatically — choose which grid this block is insured in, or draw it as two blocks."
                  : b.exposure?.method === "centroid_only"
                  ? "Located from a point, so a straddle could not be measured."
                  : "Measured by intersecting the drawn boundary with RMA's lattice."}
              </div>
              </div>
            </div>

            {/* ── YOUR ELECTION — a decision, and the only half that reaches the
                application. Never derived from the measurement above. */}
            <div style={{ ...lbl, marginBottom: 4 }}>
              <span style={{ background: "#2d4a2d", color: "#fff", padding: "1px 6px", borderRadius: 4, marginRight: 8 }}>Your election</span>
              What this block is INSURED as — it does not have to match the ground above
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end", marginBottom: 10 }}>
              <div>
                <div style={lbl}>Insured grid</div>
                {/* With ground drawn the choice is between the grids it actually
                    touches, and offering a free-text grid there would invite acres
                    onto a grid the measurement contradicts. With nothing drawn there
                    is no candidate set to offer, so the grid is typed. */}
                {(b.exposure?.grids || []).length ? (
                  <select value={b.insured_grid_id ?? ""} onChange={(e) => patch((d) => { d.blocks[i].insured_grid_id = e.target.value ? Number(e.target.value) : null; d.blocks[i].county_code = null; })} style={inp}>
                    <option value="">— choose —</option>
                    {(b.exposure?.grids || []).map((g) => <option key={g.grid} value={g.grid}>{g.grid}</option>)}
                  </select>
                ) : (
                  <input type="number" value={b.insured_grid_id ?? ""} placeholder="grid #"
                         onChange={(e) => patch((d) => { d.blocks[i].insured_grid_id = e.target.value ? Number(e.target.value) : null; d.blocks[i].county_code = null; })}
                         style={{ ...inp, width: 110 }} />
                )}
              </div>
              <div>
                <div style={lbl}>County</div>
                <select value={b.county_code || ""} onChange={(e) => patch((d) => { d.blocks[i].county_code = e.target.value || null; })} style={inp} disabled={!counties.length}>
                  <option value="">{counties.length ? "— choose —" : "…"}</option>
                  {counties.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={lbl}>Intended use</div>
                <select value={b.intended_use || ""} onChange={(e) => patch((d) => { d.blocks[i].intended_use = e.target.value || null; d.blocks[i].type_code = e.target.value === "GRAZING" ? "007" : e.target.value === "HAYING" ? "030" : null; })} style={inp}>
                  <option value="">— choose —</option>
                  <option value="HAYING">HAYING</option>
                  <option value="GRAZING">GRAZING</option>
                </select>
              </div>
              {r.cbv_varies && (
                <div>
                  <div style={lbl}>Practice</div>
                  {/* ⚠ ASK THE QUESTION THAT CHANGES THE ANSWER, NOT THE RAW
                      DIMENSION. Livingston/haying on grid 27509 publishes 66
                      practice codes and exactly TWO county base values — $329 on
                      33 of them and $447 on the other 33. Listing all 66 asked an
                      agent to choose between codes that are indistinguishable on
                      screen and identical in price, which is unanswerable.
                      The practice code is CARRIED on the actuarial row; the thing
                      that moves the premium is the base value. So the choice is
                      one option per DISTINCT value, and the codes behind it are
                      shown for provenance rather than as the decision. A
                      representative code is stored, and every code in the group
                      prices identically — so which one is stored cannot change a
                      dollar. Same rule as the rate rows' county_code. */}
                  <select
                    value={b.practice_code || ""}
                    onChange={(e) => patch((d) => { d.blocks[i].practice_code = e.target.value || null; })}
                    style={inp}
                  >
                    <option value="">— choose —</option>
                    {/* The option NAMES the practice where every code behind it is
                        one tier. Kevin's CY2027 haying shows $330 and $447 here, and
                        the higher one is NON-irrigated — the reverse of the obvious
                        guess, so a dollar figure alone invites the wrong pick on a
                        choice worth 35% of protection. A group mixing tiers, or one
                        no tier is known for, stays unnamed rather than guessed. */}
                    {Object.entries((r.cbv_choices || []).reduce((m, c) => {
                      (m[c.cbv] ||= { codes: [], tiers: new Set() });
                      m[c.cbv].codes.push(c.practice_code); m[c.cbv].tiers.add(c.tier || null);
                      return m;
                    }, {})).sort((a, b2) => Number(a[0]) - Number(b2[0])).map(([cbv, g]) => {
                      const tier = g.tiers.size === 1 ? [...g.tiers][0] : null;
                      return (
                        <option key={cbv} value={g.codes[0]}>
                          {tier ? `${tier} — ` : ""}${cbv}/ac{g.codes.length > 1 ? `  (${g.codes.length} practice codes)` : `  — practice ${g.codes[0]}`}
                        </option>
                      );
                    })}
                  </select>
                  {b.practice_code && (r.cbv_choices || []).length > 1 && (
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
                      practice {b.practice_code}
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize: 13, color: C.ink }}>
                <div style={lbl}>County base value</div>
                {r.cbv != null ? <b>${r.cbv}/ac</b>
                  : r.cbv_varies ? <span style={{ color: C.cost }}>varies by practice — choose one</span>
                  : <span style={{ color: C.muted }}>…</span>}
              </div>
              {/* ⚠ SHARE WAS MODELLED EVERYWHERE AND ASKED NOWHERE. The draft
                  carried it, quotePrf multiplies by it and prf_quote_blocks has
                  the column — but nothing on screen could change it, so every
                  quote silently assumed 100%. A grower who owns half of a
                  partnership's acres was being quoted the whole thing: it scales
                  protection AND premium, so the error is invisible in the ratio
                  and doubles the dollars. */}
              <div>
                <div style={lbl}>Share</div>
                <select
                  value={String(b.share ?? 1)}
                  onChange={(e) => patch((d) => { d.blocks[i].share = Number(e.target.value); })}
                  style={inp}
                >
                  {[1, 0.75, 0.6667, 0.5, 0.3333, 0.25].map((s) => (
                    <option key={s} value={s}>{(s * 100).toFixed(s === 0.6667 || s === 0.3333 ? 2 : 0)}%</option>
                  ))}
                </select>
              </div>
            </div>

            {/* ALLOCATION */}
            <div style={lbl}>Interval allocation — must total 100% across at least two intervals</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              {/* ⚠ NOT EVERY INTERVAL IS RATED HERE, AND THE SCREEN HAS TO SAY
                  WHICH. Measured on grid 27811 / county 055: RMA publishes no
                  JULAUG rate at 85%, and grid 27509 / county 051 rates 6 of 11
                  for grazing. Left unmarked, an agent allocates, gets a refusal
                  naming an interval, moves the percentage, and repeats — the
                  screen holds the answer and was not showing it. An unrated
                  interval is shown greyed and labelled "no rate"; it is not
                  disabled, because a rate that lands later should not need the
                  election retyped. */}
              {PRF_REVIEW.LAB.map((l) => {
                const lim = r.limits?.[l];
                const rated = r.rates ? Number(r.rates[l]) > 0 : null;
                const ambiguous = (r.rates?.__ambiguous || []).includes(l);
                // ⚠ AN INTERVAL THAT OVERLAPS ONE ALREADY ELECTED IS BLOCKED AT
                // THE INPUT. Neighbouring intervals share a month — MAY-JUN and
                // JUN-JUL both cover June — and RMA does not insure a month
                // twice. The engine refuses it too; this stops it being typed at
                // all, and names the month rather than just going grey.
                const blockedBy = (Number(b.allocation[l] || 0) > 0)
                  ? null
                  : (PRF_QUOTE_RULES.overlappingIntervals({ ...b.allocation, [l]: 1 })
                      .find((p) => p.a === l || p.b === l) || null);
                const blocked = !!blockedBy;
                const other = blocked ? (blockedBy.a === l ? blockedBy.b : blockedBy.a) : null;
                return (
                  <div key={l} style={{ width: 96, opacity: blocked ? 0.4 : rated === false ? 0.5 : 1 }}>
                    <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>{l}</div>
                    <input type="number" min="0" max="100" step="5"
                           disabled={blocked}
                           title={blocked
                             ? `Overlaps ${other} — both cover the same month, and RMA does not insure a month twice.`
                             : undefined}
                           value={Math.round((b.allocation[l] || 0) * 100)}
                           onChange={(e) => patch((d) => {
                             const v = Math.max(0, Math.min(100, Number(e.target.value) || 0)) / 100;
                             if (v > 0) d.blocks[i].allocation[l] = v; else delete d.blocks[i].allocation[l];
                           })}
                           style={{ ...inp, width: "100%", padding: "5px 6px",
                                    background: blocked ? "#f4f4f2" : "#fff",
                                    cursor: blocked ? "not-allowed" : "auto",
                                    borderColor: rated === false ? C.cost : C.line }} />
                    {blocked ? (
                      <div style={{ fontSize: 10.5, color: C.muted }}>overlaps {other}</div>
                    ) : rated === false ? (
                      <div style={{ fontSize: 10.5, color: C.cost }}>{ambiguous ? "rate varies" : "no rate"}</div>
                    ) : lim ? (
                      <div style={{ fontSize: 10.5, color: C.muted }}>{Math.round(lim.min * 100)}–{Math.round(lim.max * 100)}%</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 12.5, color: Math.abs(alloc - 1) < 0.005 ? C.gain : C.cost, marginBottom: 8 }}>
              Allocated {Math.round(alloc * 100)}%
              {/* Only once a grid HAS been chosen. Before that "this grid" names
                  nothing, and warning about the limits of a grid nobody picked
                  reads as a fault in the data rather than a step not yet taken. */}
              {b.insured_grid_id != null && !r.limits && (
                <span style={{ color: C.cost }}> · RMA percent-of-value limits are not loaded for grid {b.insured_grid_id}, so an allocation here is priced but <b>not verified as legal</b>.</span>
              )}
            </div>

            {/* THIS BLOCK'S PRICE, or exactly what is still missing */}
            {p?.result?.ok ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 22, paddingTop: 8, borderTop: `1px solid ${C.line}` }}>
                <Stat label="Protection" value={fmt(p.result.quote.total_protection)} />
                <Stat label="Total premium" value={fmt(p.result.quote.total_premium)} />
                <Stat label="Subsidy" value={fmt(p.result.quote.total_subsidy)} sub={Math.round(p.result.quote.subsidy_rate * 100) + "%"} />
                <Stat label="Producer premium" value={fmt(p.result.quote.producer_premium)} />
              </div>
            ) : (
              <div style={{ paddingTop: 8, borderTop: `1px solid ${C.line}`, fontSize: 12.5, color: C.cost, lineHeight: 1.7 }}>
                {(p?.result?.gaps || []).map((g, k) => <div key={k}>· {g.says}</div>)}
                {p?.result?.says && <div>· {p.result.says}</div>}
              </div>
            )}
          </div>
        );
      })}

      {/* ── policy-level terms + total ── */}
      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-end" }}>
          <div>
            <div style={lbl}>Crop year</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>CY{draft.crop_year}</div>
            {/* A year we could not confirm against RMA is NAMED. The quote still
                prices — refusing on a transient read would be worse — but nobody
                should hand a grower a document whose year we were guessing at. */}
            {pubYear?.source === "unconfirmed" && (
              <div style={{ fontSize: 11, color: C.cost, marginTop: 2 }}>
                not confirmed against RMA — could not read the published rate years
              </div>
            )}
          </div>
          <div>
            <div style={lbl}>Coverage level</div>
            <select value={draft.coverage_level} onChange={(e) => patch((d) => { d.coverage_level = Number(e.target.value); })} style={inp}>
              {PRF_REVIEW.COVERAGE_LEVELS.map((c) => <option key={c} value={c}>{Math.round(c * 100)}%</option>)}
            </select>
          </div>
          <div>
            <div style={lbl}>Productivity factor</div>
            <select value={draft.productivity_factor} onChange={(e) => patch((d) => { d.productivity_factor = Number(e.target.value); })} style={inp}>
              {[0.6,0.7,0.8,0.9,1.0,1.1,1.2,1.3,1.4,1.5].map((c) => <option key={c} value={c}>{Math.round(c * 100)}%</option>)}
            </select>
          </div>
          {/* Beginning farmer and rancher (MGR-25-006): extra subsidy points by BFR
              crop year — +15 in years 1-2, +13 in 3, +11 in 4, +10 in 5-10. */}
          <div>
            <div style={lbl}>Beginning farmer</div>
            <select value={draft.bfr_year == null ? "" : String(draft.bfr_year)}
                    onChange={(e) => patch((d) => { d.bfr_year = e.target.value === "" ? null : Number(e.target.value); })} style={inp}>
              <option value="">None</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((y) => (
                <option key={y} value={y}>{`Year ${y} (+${Math.round(PRF_QUOTE_RULES.bfrBonusForYear(y) * 100)} pts)`}</option>
              ))}
            </select>
          </div>
          {/* ⚠ NOTHING PRICED IS "—", NEVER 0. A zero is a claim — it says this
              quote is worth nothing — where the truth is that no block has been
              answered yet. The unfinished state must not render as a finished
              one with a bad number in it. */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 22, flexWrap: "wrap" }}>
            <Stat label="Protection" value={pricing.priced_blocks ? fmt(pricing.protection) : "—"} />
            <Stat label="Producer premium" value={pricing.priced_blocks ? fmt(pricing.producer_premium) : "—"} />
          </div>
        </div>
        {pricing.unpriced_blocks > 0 && (
          <div style={{ fontSize: 12.5, color: C.cost, marginTop: 8 }}>
            ⚠ These totals are the sum of <b>{pricing.priced_blocks}</b> of <b>{pricing.blocks.length}</b> blocks
            {" "}— <b>{Math.round(pricing.excluded_acres).toLocaleString()} of {Math.round(pricing.total_acres).toLocaleString()} acres are EXCLUDED</b>
            {" "}because {pricing.unpriced_blocks === 1 ? "a block" : `${pricing.unpriced_blocks} blocks`} still
            {" "}{pricing.unpriced_blocks === 1 ? "needs" : "need"} an answer above. The acreage matters as much as the
            block count: a total missing the home farm is a different number from one missing a corner.
          </div>
        )}
      </div>

      {/* ── compare coverage levels, and the history under them ── */}
      <PRFComparePanel draft={draft} refs={refs} isMobile={isMobile} />

      {/* ── save, only when they choose ── */}
      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <button onClick={() => save(false)} disabled={!!gaps.length || saveState?.busy} style={btn(false)}>Save as draft</button>
          <button onClick={() => save(true)} disabled={!!gaps.length || saveState?.busy} style={btn(true)}>Save as presented</button>
          <span style={{ fontSize: 12, color: C.muted, maxWidth: "62ch", lineHeight: 1.6 }}>
            Nothing has been written yet. <b>Presented</b> freezes the inputs so the figures a prospect was shown
            survive — a later change becomes a revision rather than an overwrite.
          </span>
        </div>
        {gaps.length > 0 && (
          <div style={{ fontSize: 12.5, color: C.cost, marginTop: 8, lineHeight: 1.7 }}>
            {gaps.map((g, k) => <div key={k}>· {g.block_label ? g.block_label + " — " : ""}{g.says}</div>)}
          </div>
        )}
        {saveState?.error && <div style={{ fontSize: 12.5, color: C.cost, marginTop: 8 }}>Could not save: {saveState.error}</div>}
        {saveState?.ok && (
          <div style={{ fontSize: 12.5, color: C.gain, marginTop: 8 }}>
            Saved{saveState.presented ? " and marked presented" : " as a draft"} — quote {String(saveState.id).slice(0, 8)}.
          </div>
        )}
      </div>
    </div>
  );
}
// ── PRF ADVISOR ──────────────────────────────────────────────────────────────
// The agent-side workspace. Three entry points, one engine underneath:
//
//   New Quote      — a prospect, from the map, with no customer record
//   Customers      — an existing customer's fields and current PRF structure
//   Grid Explorer  — the satellite Grid Finder: a customer's ground over the
//                    PRF lattice, and which of it sits in a cell they do not carry
//
// The book map is NOT here. "Book Review" nested the whole PRF Grid page inside
// this one, and the Grid Explorer carried a second copy of the same index map
// under a fold; both were cut 2026-11-02. PRF Grid (nav "prfgrid") is the one
// PRF map. A stored "book" tab falls back to Customers through the TABS check.
//
// ⚠ AGENT-ONLY BY CONSTRUCTION. The portal keeps rendering `PRFReviewView`
// directly — a customer has no use for a prospect quoter or a grid finder, and
// both read across the book. Routing
// the portal through this shell would put an agency workspace behind a customer
// login, which is the failure `check-portal` exists to catch. The portal path is
// deliberately untouched.
// ── THE GRID FINDER — satellite, the PRF lattice, and a farm's own ground ──
//
// Kevin, 2026-09-03: "PRF Grid Explorer needs to be a satellite map with grids
// laid over it, used to find fields that arent currently insured in a policy,
// and be able to tell which grid they are in."
//
// The old explorer was the choropleth: a projected SVG of county outlines with
// grid boxes tinted by index. Good for reading a state at a glance and useless
// for the job Kevin describes, because you cannot see the ground.
//
// ⚠ WHAT THIS MAP CAN AND CANNOT SAY, AND THE TWO HALVES ARE NOT SYMMETRIC.
// PRF insures ACRES within a grid, not fields. So:
//
//   a field in a grid the farm carries NO PRF on   -> there is definitely no PRF
//                                                     over that ground. A finding.
//   a field in a grid the farm DOES carry PRF on   -> the grid is covered. Whether
//                                                     THESE acres are is not
//                                                     knowable from the grid: the
//                                                     policy states a total, not an
//                                                     assignment.
//
// The legend says exactly that. Calling the second case "insured" would be the
// invented-explanation failure this repo opens with.
//
// ⚠ AND ONLY PART OF THE GROUND IS DRAWABLE. Measured 2026-09-03 over the 45
// farms carrying PRF: 1,963 of 5,426 of their CLUs have a geometry row — 36%.
// A map that silently draws a third of a farm reads as a farm with a third of
// the ground, so the count that could not be drawn is printed, never omitted.
function PRFGridFinder({ farmers, isMobile }) {
  const C = { ink: "#1a2e1a", ink2: "#2d4a2d", line: "#d8e2d8", muted: "#6b7d6b", cost: "#9c580f", gain: "#166f49" };
  const card = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: isMobile ? 12 : 16, marginBottom: 14 };
  const lbl = { fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 6 };
  const inp = { fontSize: 13, padding: "7px 9px", borderRadius: 7, border: `1px solid ${C.line}`, background: "#fff", color: C.ink };

  const mapRef = useRef(null);
  const layersRef = useRef({});
  const [centreGrid, setCentreGrid] = useState(null);
  const [farmerId, setFarmerId] = useState("");
  const [cropYear, setCropYear] = useState(null);
  const [fields, setFields] = useState(null);   // { drawn, undrawable, prfGrids, rows }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [addr, setAddr] = useState("");

  // ── the map ────────────────────────────────────────────────────────────
  useEffect(() => {
    let dead = false;
    loadLeaflet().then((L) => {
      if (dead || mapRef.current) return;
      const el = document.getElementById("prf-finder-map");
      if (!el) return;
      const m = L.map(el, { center: [42.9, -77.9], zoom: 9, zoomControl: true });
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
        attribution: "Esri", maxZoom: 18,
      }).addTo(m);
      // ⚠ BARE IMAGERY HAS NO ROAD NAMES, AND THAT IS HOW GROUND GETS FOUND. An
      // agent locates a field by the road it is on, so Esri's own reference pair
      // goes on top: transportation first, then boundaries and place labels.
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 18, opacity: 0.9, pane: "overlayPane",
      }).addTo(m);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 18, pane: "overlayPane",
      }).addTo(m);
      layersRef.current.fields = L.layerGroup().addTo(m);
      layersRef.current.grid = L.layerGroup().addTo(m);
      mapRef.current = m;
      setTimeout(() => m.invalidateSize(), 60);
      redrawGrid();
      m.on("moveend zoomend", redrawGrid);
    }).catch(() => setErr("The map library could not be loaded."));
    return () => { dead = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  // Which grids the farm carries PRF on, read through a ref so the Leaflet
  // handler registered once does not close over the first value forever.
  const prfGridsRef = useRef(new Set());
  useEffect(() => { prfGridsRef.current = new Set(fields?.prfGrids || []); }, [fields]);

  const redrawGrid = useCallback(() => {
    const m = mapRef.current, L = window.L;
    if (!m || !L) return;
    const g = layersRef.current.grid; if (!g) return;
    // ONE definition of the lattice — see prfDrawLattice. A cell the farm
    // carries PRF on is drawn in the covered colour, so the answer to "which
    // grid is this field in, and do they have PRF there" is on the map itself.
    const have = prfGridsRef.current;
    setCentreGrid(prfDrawLattice(L, m, g, {
      styleFor: (id) => (have.has(id) ? { color: "#166f49", weight: 2, opacity: 0.9 } : null),
      labelFor: (id) => (have.has(id)
        ? { text: `${id} · PRF`, color: "#0d4a30", bg: "rgba(214,245,229,.92)" } : null),
    }));
  }, []);
  useEffect(() => { redrawGrid(); }, [fields, redrawGrid]);

  // ── the farm's ground ──────────────────────────────────────────────────
  useEffect(() => {
    if (!farmerId) { setFields(null); return; }
    let dead = false;
    setBusy(true); setErr(null);
    (async () => {
      try {
        // The newest crop year this farm reported acreage for. Asked of the data
        // rather than assumed — a farm whose CY2027 acreage has not landed yet
        // must show CY2026 rather than an empty map.
        const yrRows = await sbFetch("policy_fields", {
          select: "crop_year", filters: [`farmer_id=eq.${farmerId}`],
          order: "crop_year.desc", limit: 1,
        });
        const YR = Number(yrRows?.[0]?.crop_year) || null;
        if (!YR) { if (!dead) { setFields({ rows: [], drawn: 0, undrawable: 0, prfGrids: [], year: null }); } return; }

        const pf = await sbFetch("policy_fields", {
          select: "clu_id,crop,acres,latitude,longitude,county,fsn,tract,field_id",
          filters: [`farmer_id=eq.${farmerId}`, `crop_year=eq.${YR}`],
        });

        // The grids this farm carries PRF on, straight from its own interval rows.
        const pols = await sbFetch("policies", {
          select: "id", filters: [`farmer_id=eq.${farmerId}`, "policy_type=eq.prf"],
        });
        let prfGrids = [];
        if (pols?.length) {
          const pp = await sbFetch("prf_policies", {
            select: "id", filters: [`policy_id=in.(${pols.map((p) => p.id).join(",")})`],
          });
          if (pp?.length) {
            const ivs = await sbFetch("prf_intervals", {
              select: "grid_id", filters: [`prf_policy_id=in.(${pp.map((p) => p.id).join(",")})`],
            });
            prfGrids = [...new Set((ivs || []).map((r) => Number(r.grid_id)).filter(Boolean))];
          }
        }

        // Geometry, where we hold it. Chunked because `in.()` is a URL.
        // ⚠ FOLDED ON BOTH SIDES — the `in.()` list AND the map key. Unfolded this drew 6,452
        // of the 15,806 CY2026 fields it holds geometry for: 59% of the book missing, 209 of 214
        // farms short, and 13 farms (C & W FARM, MERRIMAC, CHARDON…) rendering an EMPTY map while
        // every one of their fields had a boundary on file. An empty map reads as "we have no
        // boundaries for your farm", which is the worst version of this. See `cluLower`.
        const clus = [...new Set((pf || []).map((r) => cluLower(r.clu_id)).filter(Boolean))];
        const geoBy = new Map();
        for (let i = 0; i < clus.length; i += 40) {
          const chunk = clus.slice(i, i + 40).map((c) => `"${c}"`).join(",");
          const g = await sbFetch("field_geometries", {
            select: "clu_id,geometry,centroid_lat,centroid_lon,acres",
            filters: [`clu_id=in.(${chunk})`],
          }).catch(() => []);
          for (const row of g || []) geoBy.set(cluLower(row.clu_id), row);
        }

        const have = new Set(prfGrids);
        const rows = [];
        let undrawable = 0;
        for (const f of pf || []) {
          const geo = geoBy.get(cluLower(f.clu_id)) || null;
          const lat = Number(geo?.centroid_lat ?? f.latitude);
          const lon = Number(geo?.centroid_lon ?? f.longitude);
          const gid = (Number.isFinite(lat) && Number.isFinite(lon))
            ? PRF_GRID.prfGridFromLatLon(lat, lon) : null;
          if (!geo?.geometry) undrawable++;
          rows.push({
            // ⚠ AN UNSTATED ACREAGE IS NOT ZERO. `acres` is null on 875 of the
            // 15,996 CY2026 rows, and `Number(null) || 0` sums them into a total
            // that reads "22 fields · 0 ac" — which looks like zero acres rather
            // than an acreage nobody reported. Null stays null and is counted
            // separately.
            clu: f.clu_id, crop: f.crop || null,
            acres: f.acres == null || f.acres === "" ? null : Number(f.acres),
            fsn: f.fsn, tract: f.tract, field: f.field_id, county: f.county || null,
            grid: gid, covered: gid != null ? have.has(gid) : null,
            geometry: geo?.geometry || null, lat, lon,
          });
        }
        if (!dead) setFields({ rows, drawn: rows.length - undrawable, undrawable, prfGrids, year: YR });
      } catch (e) {
        if (!dead) setErr(e.message || String(e));
      } finally { if (!dead) setBusy(false); }
    })();
    return () => { dead = true; };
  }, [farmerId]);

  // ── draw the ground ────────────────────────────────────────────────────
  useEffect(() => {
    const m = mapRef.current, L = window.L;
    if (!m || !L) return;
    const g = layersRef.current.fields; if (!g) return;
    g.clearLayers();
    if (!fields?.rows?.length) return;
    const bounds = [];
    for (const r of fields.rows) {
      if (!r.geometry) continue;
      // A field in a grid with NO PRF is the FINDING and is drawn to be seen; a
      // field in a covered grid is drawn quietly, because the grid being covered
      // is not a statement that these acres are.
      const style = r.covered === false
        ? { color: "#9c580f", weight: 2, fillColor: "#f0a04b", fillOpacity: 0.35 }
        : { color: "#166f49", weight: 1.5, fillColor: "#3fa677", fillOpacity: 0.22 };
      let layer = null;
      try {
        layer = L.geoJSON(typeof r.geometry === "string" ? JSON.parse(r.geometry) : r.geometry, { style });
      } catch (_) { layer = null; }
      if (!layer) continue;
      layer.bindTooltip(
        `<b>${r.crop || "crop not stated"}</b> · ${r.acres == null ? "acreage not stated" : Math.round(r.acres) + " ac"}` +
        `<br/>PRF grid <b>${r.grid ?? "—"}</b>` +
        `<br/>${r.covered === false ? "no PRF on this grid" : r.covered ? "grid carries PRF" : "grid not resolved"}` +
        (r.fsn ? `<br/><span style="opacity:.7">FSN ${r.fsn} · tract ${r.tract} · field ${r.field}</span>` : ""),
        { sticky: true });
      layer.addTo(g);
      try { bounds.push(layer.getBounds()); } catch (_) {}
    }
    if (bounds.length) {
      let b = bounds[0];
      for (const x of bounds.slice(1)) b = b.extend(x);
      try { m.fitBounds(b, { padding: [30, 30], maxZoom: 13 }); } catch (_) {}
    }
  }, [fields]);

  const geocode = useCallback(async () => {
    const qs = addr.trim(); if (!qs) return;
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(qs)}`);
      const j = await r.json();
      const hit = j?.[0];
      if (hit && mapRef.current) mapRef.current.setView([Number(hit.lat), Number(hit.lon)], 13);
      else setErr("No match for that address.");
    } catch (_) { setErr("Address lookup failed."); }
  }, [addr]);

  const summary = useMemo(() => {
    if (!fields?.rows?.length) return null;
    let outN = 0, outAc = 0, inN = 0, inAc = 0, unk = 0, noAcres = 0;
    const outGrids = new Set();
    for (const r of fields.rows) {
      if (r.acres == null) noAcres++;
      const ac = r.acres == null ? 0 : r.acres;
      if (r.covered === false) { outN++; outAc += ac; if (r.grid) outGrids.add(r.grid); }
      else if (r.covered === true) { inN++; inAc += ac; }
      else unk++;
    }
    return { outN, outAc, inN, inAc, unk, noAcres, outGrids: [...outGrids].sort((a, b) => a - b) };
  }, [fields]);

  const prfFarmers = useMemo(
    () => (farmers || []).slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [farmers]);

  return (
    <div>
      <div style={{ ...card, marginBottom: 10 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <div style={{ minWidth: 240 }}>
            <div style={lbl}>Customer — draws their reported ground</div>
            <select value={farmerId} onChange={(e) => setFarmerId(e.target.value)} style={{ ...inp, width: "100%" }}>
              <option value="">— none, just the grid —</option>
              {prfFarmers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 220, flex: 1 }}>
            <div style={lbl}>Or go to an address</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input value={addr} onChange={(e) => setAddr(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") geocode(); }}
                     placeholder="road, town" style={{ ...inp, flex: 1 }} />
              <button onClick={geocode} style={{ ...inp, cursor: "pointer", fontWeight: 700 }}>Go</button>
            </div>
          </div>
          <div>
            <div style={lbl}>Map centre</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>
              {centreGrid != null ? <>Grid {centreGrid}</> : "—"}
            </div>
          </div>
        </div>
        {err && <div style={{ marginTop: 8, fontSize: 12.5, color: C.cost }}>{err}</div>}
        {busy && <div style={{ marginTop: 8, fontSize: 12.5, color: C.muted }}>Loading the farm's ground…</div>}
      </div>

      <div id="prf-finder-map"
           style={{ height: isMobile ? 380 : 560, border: `1px solid ${C.line}`, borderRadius: 10, marginBottom: 10 }} />

      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", fontSize: 12.5 }}>
          <span><span style={{ display: "inline-block", width: 12, height: 12, background: "#f0a04b",
                               border: "2px solid #9c580f", borderRadius: 2, marginRight: 6, verticalAlign: -2 }} />
            in a grid with <b>no PRF</b></span>
          <span><span style={{ display: "inline-block", width: 12, height: 12, background: "#3fa677",
                               border: "2px solid #166f49", borderRadius: 2, marginRight: 6, verticalAlign: -2 }} />
            in a grid that carries PRF</span>
          <span style={{ color: C.muted }}>green cell outline = a grid this farm insures</span>
        </div>
        {/* ⚠ THE TWO HALVES ARE NOT SYMMETRIC, and saying otherwise would be the
            invented explanation this repo opens with. */}
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.6, maxWidth: "84ch" }}>
          PRF insures <b>acres within a grid</b>, not fields. So a field in a grid the farm carries no PRF on
          definitely has no PRF over it — that is the gap this map exists to find. A field in a grid that does
          carry PRF sits under a covered grid; whether <i>these</i> acres are part of the insured total is not
          knowable from the grid, because the policy states a total and not an assignment.
        </div>
        {summary && (
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 22 }}>
            <div>
              <div style={lbl}>In a grid with no PRF</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.cost }}>
                {summary.outN} fields · {Math.round(summary.outAc).toLocaleString("en-US")} ac
              </div>
              {summary.outGrids.length > 0 && (
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                  grids {summary.outGrids.join(", ")}
                </div>
              )}
            </div>
            <div>
              <div style={lbl}>In a grid that carries PRF</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.gain }}>
                {summary.inN} fields · {Math.round(summary.inAc).toLocaleString("en-US")} ac
              </div>
            </div>
            {summary.unk > 0 && (
              <div>
                <div style={lbl}>Grid not resolved</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.muted }}>{summary.unk}</div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>no coordinate on file</div>
              </div>
            )}
          </div>
        )}
        {/* ⚠ A MAP THAT SILENTLY DRAWS A THIRD OF A FARM READS AS A FARM WITH A
            THIRD OF THE GROUND. Measured over the 45 farms carrying PRF, 36% of
            their CLUs have a boundary on file, so what could not be drawn is
            counted rather than omitted. */}
        {fields && fields.undrawable > 0 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: C.cost, lineHeight: 1.6, maxWidth: "84ch" }}>
            <b>{fields.undrawable} of {fields.rows.length} reported fields have no boundary on file</b> and are not
            drawn. They are counted above from their reported point, so the acreage is right and the map is
            incomplete — not the other way round.
          </div>
        )}
        {/* ⚠ THE GAP IS A FLOOR, NOT A TOTAL, AND THE REASON IS MEASURED.
            `policy_fields.latitude` is NULL on 15,242 of the 15,996 CY2026 rows
            — 95% — so a field's grid can only be resolved from a boundary
            centroid, and a field with no boundary has no coordinate either.
            Reporting "0 fields with no PRF" while 95 of a farm's fields could
            not be placed would be a clean bill of health for ground nobody
            looked at. */}
        {summary && summary.unk > 0 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: C.cost, lineHeight: 1.6, maxWidth: "84ch" }}>
            <b>{summary.unk} of {fields.rows.length} fields could not be placed in a grid</b> — no boundary and no
            reported coordinate, so nothing can be said about them either way. The gap above is therefore a
            floor, not a total.
          </div>
        )}
        {summary && summary.noAcres > 0 && (
          <div style={{ marginTop: 6, fontSize: 12.5, color: C.muted, lineHeight: 1.6, maxWidth: "84ch" }}>
            {summary.noAcres} field{summary.noAcres === 1 ? "" : "s"} carry no reported acreage, so they are
            counted but contribute no acres to the totals.
          </div>
        )}
        {fields && fields.year && (
          <div style={{ marginTop: 8, fontSize: 11.5, color: C.muted }}>
            Ground as reported for CY{fields.year} — the newest acreage year this farm has on file.
          </div>
        )}
      </div>
    </div>
  );
}

function PRFAdvisorView({ farmers, isMobile }) {
  const TABS = [
    ["quote", "New Quote", "🗺️"],
    ["customers", "Customers", "👤"],
    ["explorer", "Grid Explorer", "🔍"],
  ];
  // ⚠ A PERSISTED TAB IS UNTRUSTED INPUT. It was read straight out of
  // sessionStorage and rendered, so ANY value this build does not know — a tab
  // renamed or removed since the session started, a hand-set key — matched no
  // branch and the page rendered the tab bar over EMPTY SPACE. Found by
  // screenshotting with a bad value; every static check was green and
  // check-screens called the page clean at 795 characters, because a blank body
  // is still a body. Validate against the list that actually exists and fall
  // back, the same rule the portal's stale-tab fix already follows.
  const [tab, setTab] = useState(() => {
    try {
      const saved = sessionStorage.getItem("cloverag_prf_tab");
      return TABS.some(([k]) => k === saved) ? saved : "customers";
    } catch { return "customers"; }
  });
  const setTabPersist = (t) => { try { sessionStorage.setItem("cloverag_prf_tab", t); } catch {} setTab(t); };

  const C = { line: "#d5e2d5", muted: "#5c6f5c" };
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {TABS.map(([k, lbl, icon]) => (
          <button key={k} onClick={() => setTabPersist(k)}
            style={{ fontSize: 12.5, fontWeight: 700, padding: "7px 14px", borderRadius: 8, cursor: "pointer",
                     border: "1px solid " + (tab === k ? "#2d4a2d" : C.line),
                     background: tab === k ? "#2d4a2d" : "#fff", color: tab === k ? "#fff" : C.muted }}>
            <span style={{ marginRight: 6 }}>{icon}</span>{lbl}
          </button>
        ))}
      </div>

      {tab === "customers" && <PRFReviewView farmers={farmers} isMobile={isMobile} />}

      {tab === "explorer" && (
        <div>
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 10, maxWidth: "82ch", lineHeight: 1.6 }}>
            Satellite imagery with the PRF lattice over it. Pick a customer to draw the ground they have reported
            and see which cells it falls in — ground in a cell they carry no PRF on is highlighted. Nothing here is
            a quote.
          </div>
          <PRFGridFinder farmers={farmers} isMobile={isMobile} />
        </div>
      )}

      {tab === "quote" && <PRFNewQuoteView farmers={farmers} isMobile={isMobile} />}
    </div>
  );
}

// ── GRID EXPLORER PANEL ──────────────────────────────────────────────────────
// What RMA publishes about one grid, with no customer and no quote.
//
// ⚠ UNREACHABLE SINCE 2026-11-02. Only PRFGridMapView's `explorer` mode renders
// it, and the PRF Advisor's copy of that map (the one caller passing `explorer`)
// was cut. It and the `explorer` prop come out together in a PRFGridMapView
// change; they were left in place only so this cut did not edit that view.
//
// ⚠ IT SHOWS NO DOLLARS, ON PURPOSE. A premium needs acres, a productivity
// factor, a share and a percent of value — every one a producer's ELECTION
// rather than a property of the grid. Defaulting them so the panel could print
// a figure would put a number in front of an agent that no producer chose, and
// it would read exactly like a quote. The grid determines the RATE and the
// county base value; those are shown, and quoting is a separate deliberate act.
//
// It reads only reference tables — no farmer data — so it is safe on any
// surface the map itself is safe on.
function PRFGridExplorerPanel({ grid, cropYear, county, isMobile, onStartQuote }) {
  const [cov, setCov] = useState(0.85);
  // A grid spans several counties and a premium rate belongs to ONE of them, so
  // the panel picks rather than pools. Null follows the map's own county.
  const [rateCounty, setRateCounty] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!grid) return;
    let dead = false;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const [indexRows, rateRows, cbvRows, limitRows, settledRows] = await Promise.all([
          sbFetch("prf_index_history", {
            select: "year,interval_code,index_value",
            filters: [`grid_id=eq.${grid}`, `source_crop_year=eq.${cropYear}`], order: "year.asc", maxPages: 5,
          }),
          sbFetch("prf_premium_rates", {
            select: "interval_code,coverage_level,base_rate,type_code,county_code,practice_code",
            filters: [`grid_id=eq.${grid}`, `crop_year=eq.${cropYear}`],
          }),
          sbFetch("prf_county_base_values", {
            select: "crop_year,state_code,county_code,grid_id,type_code,practice_code,county_base_value",
            filters: [`grid_id=eq.${grid}`, `crop_year=eq.${cropYear}`], maxPages: 3,
          }),
          sbFetch("prf_interval_limits", {
            select: "grid_id,crop_year,interval_code,min_percent,max_percent",
            filters: [`grid_id=eq.${grid}`, `crop_year=eq.${cropYear}`], limit: 50,
          }),
          // Settled years the published vintage cannot carry — see the note in
          // the policy loader above and mergeSettledYears.
          sbFetch("prf_grid_indices", {
            select: "crop_year,interval_code,final_index_value",
            filters: [`grid_id=eq.${grid}`, "final_index_value=not.is.null"],
            order: "crop_year.asc", maxPages: 3,
          }),
        ]);
        if (dead) return;
        setData(PRF_REVIEW.buildGridExplorer({
          grid, cropYear, coverageLevel: cov, county,
          indexRows, rateRows, cbvRows, limitRows, settledRows, sourceCropYear: cropYear,
          countyCode: rateCounty,
        }));
      } catch (e) { if (!dead) setErr(e?.message || String(e)); }
      finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, [grid, cropYear, cov, county, rateCounty]);

  const C = { ink: "#1a2e1a", ink2: "#2d4a2d", line: "#d8e2d8", muted: "#6b7d6b", cost: "#9c580f", gain: "#166f49" };
  const card = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: isMobile ? 12 : 16, marginTop: 14 };
  const th = { textAlign: "right", padding: "6px 8px", fontSize: 11, letterSpacing: ".05em", textTransform: "uppercase", color: C.muted, fontWeight: 600, whiteSpace: "nowrap" };
  const td = { textAlign: "right", padding: "6px 8px", borderTop: `1px solid ${C.line}`, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const PC = (x) => x == null ? "—" : Math.round(x * 100) + "%";

  if (loading && !data) return <div style={{ ...card, color: C.muted }}>Loading grid {grid}…</div>;
  if (err) return <div style={{ ...card, color: C.cost }}>Couldn’t load grid {grid}: {err}</div>;
  if (!data) return null;

  const ivs = data.intervals.intervals;
  const named = data.cbv_by_use.filter((c) => c.use_is_named);
  const unnamed = data.cbv_by_use.filter((c) => !c.use_is_named);

  return (
    <div style={card}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Grid {grid} — what RMA publishes</div>
        <div style={{ fontSize: 12, color: C.muted }}>
          CY{cropYear}{county ? ` · ${county} County` : ""} ·{" "}
          {data.record.available
            ? `${data.record.years} years (${data.record.first_year}–${data.record.last_year})`
            : "no published index history loaded for this vintage"}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted }}>Coverage</span>
          <select value={cov} onChange={(e) => setCov(Number(e.target.value))}
                  style={{ fontSize: 13, padding: "5px 8px", borderRadius: 7, border: `1px solid ${C.line}`, background: "#fff", color: C.ink }}>
            {PRF_REVIEW.COVERAGE_LEVELS.map((c) => <option key={c} value={c}>{Math.round(c * 100)}%</option>)}
          </select>
          {onStartQuote && (
            <button onClick={() => onStartQuote(data.quote_seed)}
                    style={{ fontSize: 13, fontWeight: 700, padding: "7px 14px", borderRadius: 8, cursor: "pointer",
                             border: "1px solid #2d4a2d", background: "#2d4a2d", color: "#fff" }}>
              Start quote in this grid →
            </button>
          )}
        </div>
      </div>

      {/* County base value per intended use. Listed, never averaged — one grid
          spans several counties and a mean of two counties' values is a figure
          RMA never published and no policy could be written at. */}
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 6 }}>
        County base value — per intended use, per county
      </div>
      {named.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.cost, marginBottom: 12 }}>
          RMA publishes no county base value for this grid in CY{cropYear}.
        </div>
      ) : (
        <div style={{ overflowX: "auto", marginBottom: 12 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13, minWidth: 420 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: "left" }}>Use</th><th style={{ ...th, textAlign: "left" }}>County</th>
              <th style={th}>$ / acre</th><th style={{ ...th, textAlign: "left" }}>Basis</th>
            </tr></thead>
            <tbody>
              {named.map((c) => (
                <tr key={`${c.use}|${c.county_code}`}>
                  <td style={{ ...td, textAlign: "left", fontWeight: 700 }}>{c.use}</td>
                  <td style={{ ...td, textAlign: "left" }}>{c.county_code}</td>
                  <td style={{ ...td, fontWeight: 700 }}>
                    {c.cbv != null ? "$" + c.cbv : `$${c.min_cbv}–$${c.max_cbv}`}
                  </td>
                  <td style={{ ...td, textAlign: "left", color: c.cbv != null ? C.muted : C.cost, whiteSpace: "normal" }}>
                    {c.cbv != null
                      ? `one value across ${c.practices} practices`
                      : `varies by practice — ${c.detail}. A quote resolves this on the producer's own practice; it is not averaged here.`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {unnamed.length > 0 && (
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          RMA also publishes type {unnamed.map((u) => u.type_code).join(", ")} here
          {unnamed[0]?.cbv != null ? ` at $${unnamed[0].cbv}/ac` : ""} — <b>meaning not established</b>, so it is
          shown as the raw code rather than given a label.
        </div>
      )}

      {/* 77-year interval behaviour, from the shared builder. */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted }}>
          Interval behaviour at {Math.round(cov * 100)}% coverage
        </div>
        {(data.rate_counties || []).length > 1 && (
          <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
            Rates for county
            <select value={data.rate_county || ""} onChange={(e) => setRateCounty(e.target.value)}
                    style={{ fontSize: 12.5, padding: "3px 6px", borderRadius: 6, border: `1px solid ${C.line}`, background: "#fff", color: C.ink }}>
              {data.rate_counties.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <span style={{ color: C.muted }}>— this grid spans {data.rate_counties.length}</span>
          </label>
        )}
      </div>
      {!data.record.available ? (
        <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.6, maxWidth: "74ch" }}>
          RMA’s published index history for the <b>CY{cropYear} vintage</b> is not loaded for this grid, so nothing
          here has been replayed. It is not filled in from another year: RMA restates the index history every crop
          year, so another year’s record is a different record.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%", minWidth: 560 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: "left" }}>Interval</th>
              <th style={th}>Triggers</th><th style={th}>Payment when it pays</th>
              <th style={th}>Last 20 yrs</th><th style={th}>Index drift</th>
              <th style={th}>Rate{data.rate_county ? ` · county ${data.rate_county}` : ""}</th>
            </tr></thead>
            <tbody>
              {ivs.map((i) => (
                <tr key={i.interval}>
                  <td style={{ ...td, textAlign: "left", fontWeight: 700 }}>{i.interval}</td>
                  {!i.available ? (
                    <td style={{ ...td, textAlign: "left", color: C.muted }} colSpan={5}>
                      not published for this grid
                    </td>
                  ) : (<>
                    <td style={td}>{PC(i.trigger_freq)}</td>
                    <td style={td}>{i.mean_factor_when_paid == null ? "—" : (i.mean_factor_when_paid * 100).toFixed(1) + "%"}</td>
                    <td style={td}>{PC(i.recent_trigger_freq)}</td>
                    <td style={{ ...td, color: i.recent_vs_long_index >= 0 ? C.gain : C.cost }}>
                      {(i.recent_vs_long_index >= 0 ? "+" : "") + i.recent_vs_long_index.toFixed(3)}
                    </td>
                    <td style={td}>
                      {(() => {
                        const r = data.rates_by_use[0];
                        if (!r) return "—";
                        if ((r.ambiguous || []).includes(i.interval)) return <span style={{ color: C.cost }}>varies</span>;
                        return r.rates[i.interval] == null ? "—" : (r.rates[i.interval] * 100).toFixed(2) + "%";
                      })()}
                    </td>
                  </>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 12, color: C.muted, lineHeight: 1.6, maxWidth: "88ch" }}>
        “Triggers” is the share of years the index fell below <b>{Math.round(cov * 100)}%</b> — it moves with the
        coverage level and means nothing without it. “Payment when it pays” is the average payment factor in the
        years that paid, a different question from how often. “Index drift” is the last 20 years’ mean index minus
        the full-record mean; it is a difference, not a trend, and 20 years is one sample. These are{" "}
        <b>rainfall-index</b> years, not observed forage losses — no crop outcome was measured.
        {data.limits_status !== "OK" && (
          <> RMA percent-of-value limits are not loaded for this grid, so any allocation built here would be
          priced but <b>not verified as legal</b>.</>
        )}
      </div>
    </div>
  );
}

function PRFGridPage({ farmers, isMobile, mode = "admin" }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 0 }}>
        <span style={{ fontSize: 28 }}>🌧️</span>
        <div>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 22, fontWeight: 700, color: "#1a2e1a" }}>PRF Grid</div>
          <div style={{ fontSize: 12, color: "#60735f" }}>Pasture, Rangeland &amp; Forage — Rainfall Index</div>
        </div>
      </div>
      <PRFGridMapView farmers={farmers} isMobile={isMobile} mode={mode} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DRP RMA-DIRECT QUOTER (SHADOW) — computes premium straight from RMA ADM data
// via /api/drp-quote (P18-1 Plan 83 5000-iter simulation). Side-by-side with the
// Ever.Ag feed so we can verify our numbers before making this the source of truth.
// ─────────────────────────────────────────────────────────────────────────────
function normQuarterKey(q) {
  if (!q) return null;
  let m = String(q).match(/Q\s*(\d)\s*[-' ]?\s*(\d{2,4})/i);   // "Q3 2026", "Q3-26"
  if (m) { let y = m[2].length === 2 ? 2000 + +m[2] : +m[2]; return `${y}-Q${m[1]}`; }
  m = String(q).match(/(\d{4})-?Q(\d)/i);                       // "2026-Q3"
  if (m) return `${m[1]}-Q${m[2]}`;
  return null;
}

function DRPRmaQuoter({ isMobile, mode = "admin", defaultState = "NY" }) {
  const [rows, setRows] = useState([]);            // table-mode rows (all quarters x class)
  const [salesDate, setSalesDate] = useState(null);
  const [coverage, setCoverage] = useState(0.95);
  const [pf, setPf] = useState(1.0);
  const [pounds, setPounds] = useState("");        // optional declared production (lbs)
  const [customOpen, setCustomOpen] = useState(false);
  const [quoteState, setQuoteState] = useState(({ NY: "36", PA: "42", MI: "26" }[(defaultState || "NY").toUpperCase()]) || "36"); // RMA state FIPS — 36=NY, 26=MI, 42=PA (seeded from farmer's state in portal)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState([]);      // ~13 months of drp_board for market context
  const [ctxClass, setCtxClass] = useState("IV");  // which class the context panel tracks
  const [lookback, setLookback] = useState({ days: 7, label: "1 week" });  // market-context comparison period
  const [customN, setCustomN] = useState("");      // custom lookback amount
  const [customUnit, setCustomUnit] = useState("days");

  const poundsNum = parseFloat(String(pounds).replace(/[^0-9.]/g, "")) || 0;
  const dollarsActive = poundsNum > 0;

  // Load the quoting board (all quarters x both classes) from the RMA-direct engine
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const prodParam = dollarsActive ? `&prod=${poundsNum}` : "";
        const r = await fetch(`/api/drp-quote?table=1&state=${quoteState}&coverage=${coverage}&pf=${pf}${prodParam}`).then(x => x.json());
        if (cancelled) return;
        if (!r.ok) throw new Error(r.error || "quote failed");
        setRows(r.rows || []);
        setSalesDate(r.sales_date);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [coverage, pf, poundsNum, dollarsActive, quoteState]);

  // ~13 months of net-floor history (RMA-sourced drp_board) for the market-context panel
  useEffect(() => {
    (async () => {
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 400);
      const h = await sbFetch("drp_board", {
        select: "price_date,quarter,milk_class,net_floor,price",
        filters: [`price_date=gte.${cutoff.toISOString().slice(0, 10)}`],
        order: "price_date.asc",
      }).catch(() => []);
      setHistory(h || []);
    })();
  }, []);

  const daysToStart = (startIso) => {
    if (!startIso) return null;
    return Math.round((new Date(startIso + "T00:00:00") - new Date()) / 86400000);
  };
  const covPct = (coverage * 100).toFixed(0);
  const d2 = (v) => v == null ? "—" : "$" + (+v).toFixed(2);
  const money0 = (v) => v == null ? "—" : "$" + Math.round(v).toLocaleString();

  // ── table for one class (mobile-tuned so the whole board fits one screenshot) ──
  const compact = isMobile;
  const qShort = (q) => compact && q ? q.replace(/^Q(\d)\s+\d{2}(\d{2})$/, "Q$1 '$2") : q;
  const dCell = (v) => d2(v);   // "$xx.xx" — fits at mobile font sizes
  const mCell = (v) => v == null ? "—" : (compact ? Math.round(v / 1000) + "k" : "$" + Math.round(v).toLocaleString());
  const ClassTable = ({ cls }) => {
    const crows = rows.filter(r => r.class === cls);
    const th = { fontSize: compact ? 9 : 10.5, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: compact ? 0.1 : 0.4, padding: compact ? "6px 4px" : "8px 10px", textAlign: "right", whiteSpace: "nowrap" };
    const td = { fontSize: compact ? 12 : 14, color: "#1a2e1a", padding: compact ? "7px 4px" : "10px 10px", textAlign: "right", whiteSpace: "nowrap" };
    const noScroll = compact && !dollarsActive;
    return (
      <div style={{ background: "#fff", border: "1px solid #dde5dd", borderRadius: 12, overflow: "hidden", marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ background: "#eef4ee", padding: compact ? "8px 12px" : "10px 14px", fontFamily: "'Lora','Georgia',serif", fontSize: compact ? 14 : 15, fontWeight: 800, color: "#2d4a2d", letterSpacing: 0.5 }}>CLASS {cls}</div>
        <div style={{ overflowX: noScroll ? "visible" : "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: noScroll ? 0 : (dollarsActive ? (compact ? 540 : 640) : (compact ? 0 : 460)), tableLayout: compact ? "fixed" : "auto" }}>
            <thead>
              <tr style={{ background: "#f7faf7", borderBottom: "1px solid #e4ece4" }}>
                <th style={{ ...th, textAlign: "left", width: compact ? "26%" : "auto" }}>{compact ? "Qtr" : "Quarter"}</th>
                <th style={th}>Price</th>
                <th style={th}>{compact ? `${covPct}%` : `${covPct}% Floor`}</th>
                <th style={th}>{compact ? "Prem" : "Premium"}</th>
                <th style={{ ...th, color: "#2d6e2d" }}>{compact ? "Net" : "Net Floor"}</th>
                {dollarsActive && <th style={th}>{compact ? "Cost" : "Your Cost"}</th>}
                {dollarsActive && <th style={th}>{compact ? "Liab" : "Liability"}</th>}
              </tr>
            </thead>
            <tbody>
              {crows.map((r) => {
                const d = daysToStart(r.quarter_start);
                return (
                  <tr key={r.milk_yield_id + cls} style={{ borderBottom: "1px solid #f0f4f0" }}>
                    <td style={{ ...td, textAlign: "left" }}>
                      <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: compact ? 12.5 : 15, fontWeight: 700 }}>{qShort(r.quarter)}</div>
                      <div style={{ fontSize: compact ? 9 : 11, color: d != null && d <= 30 ? "#c07010" : "#677167" }}>{d != null ? (d < 0 ? "active" : `${d}d`) : ""}{!compact && d != null && d >= 0 ? " to start" : ""}</div>
                    </td>
                    <td style={td}>{dCell(r.expected_price)}</td>
                    <td style={td}>{dCell(r.floor)}</td>
                    <td style={{ ...td, color: "#c0392b" }}>{dCell(r.premium_per_cwt)}</td>
                    <td style={{ ...td, fontWeight: 800, color: "#2d6e2d", fontFamily: "'Lora','Georgia',serif", fontSize: compact ? 13.5 : 16 }}>{dCell(r.net_floor)}</td>
                    {dollarsActive && <td style={{ ...td, fontWeight: 700 }}>{mCell(r.producer_premium)}</td>}
                    {dollarsActive && <td style={td}>{mCell(r.liability)}</td>}
                  </tr>
                );
              })}
              {crows.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "#677167" }}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const selWrap = { display: "flex", alignItems: "center", gap: 8 };
  const sel = { padding: "7px 10px", borderRadius: 8, border: "1px solid #cfdccf", fontSize: 14, background: "#fff", color: "#1a2e1a" };
  const lbl = { fontSize: 11, color: "#60735f", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 };

  return (
    <div style={{ paddingTop: 16 }}>
      {/* Header band (matches the DRP Daily Prices layout) */}
      <div style={{ background: "linear-gradient(135deg,#1a2e1a,#2d4a2d)", borderRadius: "12px 12px 0 0", padding: isMobile ? "16px 18px" : "20px 26px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: isMobile ? 18 : 22, fontWeight: 800, color: "#fff" }}>DRP Quote — RMA Direct</div>
          <div style={{ fontSize: 12, color: "#9fc09f", marginTop: 2 }}>Computed from RMA actuarial files (Plan 83)</div>
        </div>
        <div style={{ textAlign: isMobile ? "left" : "right" }}>
          <div style={{ fontSize: 13, color: "#cfe0cf" }}>{salesDate ? new Date(salesDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : ""}</div>
          <div style={{ fontSize: 12, color: "#9fc09f", marginTop: 2 }}>{covPct}% Coverage · PF {pf.toFixed(2)} · {{"36":"New York","26":"Michigan","42":"Pennsylvania","24":"Maryland"}[quoteState] || "New York"}</div>
        </div>
      </div>

      {/* Controls bar */}
      <div style={{ background: "#fff", border: "1px solid #dde5dd", borderTop: "none", borderRadius: "0 0 12px 12px", padding: "12px 16px", marginBottom: 16, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
        <div style={selWrap}>
          <span style={lbl}>Coverage</span>
          <select style={sel} value={coverage} onChange={e => setCoverage(+e.target.value)}>
            {[0.95, 0.90, 0.85, 0.80].map(c => <option key={c} value={c}>{(c * 100).toFixed(0)}%</option>)}
          </select>
        </div>
        <button onClick={() => setCustomOpen(o => !o)} style={{ ...sel, cursor: "pointer", fontWeight: 700, color: "#4a6e4a" }}>
          {customOpen ? "▾" : "▸"} Add pounds &amp; protection factor
        </button>
        <div style={{ ...selWrap, marginLeft: "auto" }}>
          <span style={lbl}>State</span>
          <select style={sel} value={quoteState} onChange={e => setQuoteState(e.target.value)}>
            <option value="36">New York</option>
            <option value="26">Michigan</option>
            <option value="42">Pennsylvania</option>
            <option value="24">Maryland</option>
          </select>
        </div>
      </div>

      {customOpen && (
        <div style={{ background: "#f7faf7", border: "1px solid #dde5dd", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center" }}>
          <div style={selWrap}>
            <span style={lbl}>Declared Milk (lbs)</span>
            <input style={{ ...sel, width: 150 }} type="text" inputMode="numeric" placeholder="e.g. 1,000,000" value={pounds ? Number(pounds).toLocaleString("en-US") : ""} onChange={e => setPounds(e.target.value.replace(/[^0-9]/g, ""))} />
          </div>
          <div style={selWrap}>
            <span style={lbl}>Protection Factor</span>
            <select style={sel} value={pf} onChange={e => setPf(+e.target.value)}>
              {Array.from({ length: 11 }, (_, i) => +(1 + i * 0.05).toFixed(2)).map(f => <option key={f} value={f}>{f.toFixed(2)}</option>)}
            </select>
          </div>
          {dollarsActive && <div style={{ fontSize: 12, color: "#4a6e4a" }}>Showing dollar premium &amp; liability for <b>{poundsNum.toLocaleString()} lbs</b> at PF {pf.toFixed(2)}.</div>}
        </div>
      )}

      {err && <div style={{ background: "#fdf0ee", border: "1px solid #f0c0b8", borderRadius: 8, padding: 12, color: "#b03020", marginBottom: 16, fontSize: 13 }}>⚠ {err}</div>}

      {loading ? <div style={{ color: "#60735f", padding: 20 }}>Computing premiums from RMA actuarial data…</div> : (
        <>
          <ClassTable cls="III" />
          <ClassTable cls="IV" />

          {/* ── Market Context: where each quarter sits vs a week/month ago and the 12-mo range ── */}
          {(() => {
            const qrows = rows.filter(r => r.class === ctxClass).sort((a, b) => a.milk_yield_id - b.milk_yield_id);
            const baseIso = salesDate || new Date().toISOString().slice(0, 10);
            const ago = (series, n) => { const t = new Date(baseIso + "T12:00:00Z"); t.setUTCDate(t.getUTCDate() - n); const ts = t.toISOString().slice(0, 10); let v = null; for (const s of series) { if (s.d <= ts) v = s; else break; } return v; };  // → {d,nf} or null
            const delta = (cur, prev) => prev == null ? null : +(cur - prev).toFixed(2);
            const fmtDate = (iso) => iso ? new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "";
            const ord = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };  // 1→1st, 2→2nd, 11→11th
            const dChip = (v) => v == null ? <span style={{ color: "#c4c4c4" }}>—</span> : <span style={{ color: v > 0 ? "#2d7a2d" : v < 0 ? "#c0392b" : "#888", fontWeight: 700 }}>{v > 0 ? "▲ " : v < 0 ? "▼ " : ""}{"$" + Math.abs(v).toFixed(2)}</span>;
            const PRESETS = [[7, "1W", "1 week"], [14, "2W", "2 weeks"], [30, "1M", "1 month"], [90, "3M", "3 months"], [180, "6M", "6 months"], [365, "1Y", "1 year"]];
            const setCustom = (n, unit) => { const num = parseInt(n); if (num > 0) { const mult = unit === "weeks" ? 7 : unit === "months" ? 30 : 1; setLookback({ days: num * mult, label: `${num} ${unit}` }); } };
            return (
              <div style={{ background: "#fff", border: "1px solid #dde5dd", borderRadius: 12, overflow: "hidden", marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <div style={{ background: "#eef4ee", padding: compact ? "8px 12px" : "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <span style={{ fontFamily: "'Lora','Georgia',serif", fontSize: compact ? 14 : 16, fontWeight: 800, color: "#2d4a2d" }}>📈 Market Context — Net Floor</span>
                  <div style={{ display: "flex", gap: 4 }}>
                    {["III", "IV"].map(c => (
                      <button key={c} onClick={() => setCtxClass(c)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #cfdccf", background: ctxClass === c ? "#2d4a2d" : "#fff", color: ctxClass === c ? "#fff" : "#4a6e4a", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Class {c}</button>
                    ))}
                  </div>
                </div>
                {/* Compare-to period selector — presets + custom amount/unit */}
                <div style={{ background: "#f7faf7", borderBottom: "1px solid #eef2ee", padding: compact ? "6px 10px" : "8px 14px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: 0.3 }}>Compare to</span>
                  {PRESETS.map(([d, sh, lb]) => { const on = lookback.days === d && !customN; return (
                    <button key={d} onClick={() => { setLookback({ days: d, label: lb }); setCustomN(""); }} style={{ padding: "3px 8px", borderRadius: 6, border: "1px solid #cfdccf", background: on ? "#4a6e4a" : "#fff", color: on ? "#fff" : "#4a6e4a", fontWeight: 700, fontSize: 11.5, cursor: "pointer" }}>{sh}</button>
                  ); })}
                  <span style={{ fontSize: 11, color: "#677167" }}>or</span>
                  <input type="number" min="1" placeholder="#" value={customN} onChange={e => { setCustomN(e.target.value); setCustom(e.target.value, customUnit); }} style={{ width: 46, padding: "4px 6px", borderRadius: 6, border: `1px solid ${customN ? "#4a6e4a" : "#cfdccf"}`, fontSize: 12 }} />
                  <select value={customUnit} onChange={e => { setCustomUnit(e.target.value); setCustom(customN, e.target.value); }} style={{ padding: "4px 6px", borderRadius: 6, border: "1px solid #cfdccf", fontSize: 12, background: "#fff" }}>
                    <option value="days">days</option><option value="weeks">weeks</option><option value="months">months</option>
                  </select>
                  <span style={{ fontSize: 11, color: "#677167" }}>ago</span>
                </div>
                <div style={{ padding: compact ? "4px 10px 10px" : "6px 14px 12px" }}>
                  {qrows.map(r => {
                    const key = normQuarterKey(r.quarter);
                    const series = history.filter(h => normQuarterKey(h.quarter) === key && h.milk_class === "Class " + ctxClass).map(h => ({ d: h.price_date, nf: +h.net_floor })).filter(s => !isNaN(s.nf)).sort((a, b) => a.d.localeCompare(b.d));
                    const cur = r.net_floor;
                    const past = ago(series, lookback.days);
                    const dv = delta(cur, past && past.nf);
                    const nfs = series.map(s => s.nf);
                    let minS = null, maxS = null;  // track the date of the 12-mo low/high (most recent occurrence on ties)
                    for (const s of series) { if (!minS || s.nf <= minS.nf) minS = s; if (!maxS || s.nf >= maxS.nf) maxS = s; }
                    const min = minS ? minS.nf : cur, max = maxS ? maxS.nf : cur;
                    const pct = nfs.length ? Math.round(100 * nfs.filter(x => x <= cur).length / nfs.length) : null;
                    const pos = max > min ? Math.max(0, Math.min(100, Math.round((cur - min) / (max - min) * 100))) : 50;
                    // ⚠ WHERE THE FLOOR SITS IS A FACT; WHEN TO ACT ON IT IS NOT OURS TO SAY.
                    // This strip renders in the CUSTOMER portal — QuotersHub mounts with
                    // mode="portal" and passes it to DRPRmaQuoter — so the "good time to
                    // lock" / "weak floor" notes that used to hang under this badge were
                    // timing advice in front of a grower. The POSITION survives because it
                    // is measured against this exact coverage's own last 12 months of
                    // offers; the verdict does not. Do not re-add a note field here.
                    const sig = pct == null ? null : pct >= 67 ? { t: "Near highs", c: "#2d7a2d", bg: "#edf7ed" } : pct <= 33 ? { t: "Near lows", c: "#c0392b", bg: "#fdf0ee" } : { t: "Mid-range", c: "#c07010", bg: "#fdf3e7" };
                    return (
                      <div key={r.milk_yield_id} style={{ display: "grid", gridTemplateColumns: compact ? "62px 1fr" : "100px 92px 1fr 116px", alignItems: "center", gap: compact ? 8 : 14, padding: "10px 2px", borderBottom: "1px solid #f0f4f0" }}>
                        <div>
                          <div style={{ fontFamily: "'Lora','Georgia',serif", fontWeight: 700, fontSize: compact ? 12.5 : 15 }}>{qShort(r.quarter)}</div>
                          <div style={{ fontWeight: 800, color: "#2d6e2d", fontSize: compact ? 14 : 17, fontFamily: "'Lora','Georgia',serif" }}>${cur.toFixed(2)}</div>
                        </div>
                        {!compact && <div style={{ fontSize: 13 }}><div style={{ color: "#677167", fontSize: 10, marginBottom: 2 }}>vs {lookback.label} ago{past ? ` · ${fmtDate(past.d)}` : ""}</div>{dChip(dv)}{past && <span style={{ color: "#bcc8bc", fontSize: 11, marginLeft: 5 }}>(${past.nf.toFixed(2)})</span>}</div>}
                        <div>
                          <div style={{ position: "relative", height: 8, background: "linear-gradient(90deg,#fbe3df,#fcf3df,#e6f4e6)", borderRadius: 4 }}>
                            <div style={{ position: "absolute", left: `calc(${pos}% - 4px)`, top: -2, width: 8, height: 12, background: "#1a2e1a", borderRadius: 2 }} />
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: 10.5, color: "#677167", marginTop: 3 }}>
                            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>${min.toFixed(2)} lo{minS && <span style={{ fontSize: 9.5, color: "#bcc8bc" }}>{fmtDate(minS.d)}</span>}</span>
                            {pct != null && <span style={{ fontWeight: 700, color: "#5a7a5a", paddingTop: 1 }}>{ord(pct)} pctile</span>}
                            <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.25 }}>${max.toFixed(2)} hi{maxS && <span style={{ fontSize: 9.5, color: "#bcc8bc" }}>{fmtDate(maxS.d)}</span>}</span>
                          </div>
                          {compact && <div style={{ fontSize: 10.5, marginTop: 3 }}>vs {lookback.label}: {dChip(dv)} {sig && <span style={{ color: sig.c, fontWeight: 700 }}>· {sig.t}</span>}</div>}
                        </div>
                        {!compact && sig && <div style={{ textAlign: "center" }}><span style={{ background: sig.bg, color: sig.c, fontWeight: 700, fontSize: 11, padding: "3px 9px", borderRadius: 12, whiteSpace: "nowrap" }}>{sig.t}</span></div>}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 11, color: "#677167", marginTop: 8, lineHeight: 1.4 }}>
                    <b>Net floor</b> = your protected <b>class</b> price after premium — a higher floor protects more of the milk price. Δ shows the move vs your chosen period (the value then is in parentheses); the bar + percentile place today against the floors this coverage has actually offered in the last 12 months. Whether to buy is your decision.
                  </div>
                </div>
              </div>
            );
          })()}

          <div style={{ fontSize: 11.5, color: "#677167", lineHeight: 1.5 }}>
            🧮 Computed from RMA's own ADM (Plan 83) via the P18-1 5,000-iteration simulation. <b>Premium</b> = your subsidized cost/cwt (44% subsidy at 95%). <b>Net Floor</b> = {covPct}% floor − premium. Penny-exact vs the daily board; dollar premium at high protection factors within ~0.04%. <b>DRP settles on the announced CLASS price, not on your milk cheque.</b> Your mailbox price is that plus your own basis (PPD, components, hauling), which DRP does not cover. This quoter does not model that basis, so the floor above is what DRP protects rather than what the milk cheque will be.
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MILK MARKET INTELLIGENCE — on-demand AI brief (internal/admin only)
// Pulls our milk data (Class III/IV announced + DRP forward curve + FMMO pool +
// NY supply), hands it to Claude via /api/market?fn=milk-brief, renders the
// narrative. Generated on demand — not on a schedule.
// ─────────────────────────────────────────────────────────────────────────────
// ── AN ON-DEMAND BRIEF GOES STALE, AND ONLY SOME READERS CAN REFRESH IT ─────
// The three AI market briefs are generated by an agent pressing a button, not by
// a cron. Home removed them for exactly that reason — Kevin, 2026-08-22: "if
// briefs arent automatic, remove them" — and the Markets hub kept them, where
// `marketHubTabs` puts Grain MI, Milk MI and Cattle MI in front of PORTAL users
// who have no button to press.
//
// Measured on Chris Dawson's portal 2026-09-09: the Grain MI brief read "Data as
// of 2026-08-20 · generated 8/21/2026" and ended "Corn: this is a strong window —
// sell cash now" on WNY basis, to a manager of eight MICHIGAN and OHIO dairy
// operations, nineteen days later. Nothing on the page said it was out of date —
// `relTime` returns a bare date past 24h, so "8/21/2026" was the entire signal.
//
// ⚠ THE AGE IS ALWAYS PRINTED, WHATEVER THE THRESHOLD DECIDES. That is what keeps
// this honest: BRIEF_FRESH_DAYS is an agency judgment rather than a measurement —
// the brief reads daily board closes and weekly CFTC positioning, so two days is
// a reasonable bar and it is Kevin's to set — but no reader is ever shown a brief
// without being told how old it is, so the constant cannot mislead anyone on its
// own.
//
// What it decides is narrower: whether a reader who CANNOT regenerate is shown
// stale CONTENT. An agent sees the brief plus a banner and the Regenerate button,
// because they can act on it. A portal reader sees a NAMED ABSENCE — this repo's
// own doctrine, the same shape Home uses for a source it meant to read and could
// not — and the live headlines above it are unaffected, because those ARE
// automatic.
const BRIEF_FRESH_DAYS = 2;
function briefAgeDays(generatedAt) {
  if (!generatedAt) return null;
  const t = Date.parse(generatedAt);
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}
function briefIsStale(generatedAt) {
  const d = briefAgeDays(generatedAt);
  // An UNDATED brief is stale: we cannot show it as current when we cannot say
  // when it was written. Failing closed is the safe direction for a page that
  // ends in "sell cash now".
  return d == null || d >= BRIEF_FRESH_DAYS;
}
// Content is shown when the brief is current, OR when the reader can refresh it.
// ⚠ THE AGE IS STATED; THE CONTENT IS NEVER WITHHELD.
// An earlier cut of this hid a stale brief from any reader who could not press
// Regenerate. Kevin, 2026-09-10: "Brief staleness, just make it obvious the date
// the last brief Ran." Narrower, and it is the right scope — the reason hiding
// looked necessary was a nineteen-day-old brief that ended "sell cash now", and
// that sentence cannot be written any more: the timing bar in GROUND_RULES took
// it out of all three briefs. What is left is a market read that is simply old,
// and an old market read a reader can DATE is information, not a hazard.
//
// So there is no briefContentVisible() any more. A rule that can hide content is
// a rule someone has to keep correct forever; a rule that can only annotate it
// cannot mislead anyone on its own.
function BriefRunDate({ brief, canRegenerate, what }) {
  if (!brief) return null;
  const d = briefAgeDays(brief.generated_at);
  const stale = briefIsStale(brief.generated_at);
  const age = d == null ? "an unknown time ago" : d === 0 ? "earlier today" : d === 1 ? "yesterday" : `${d} days ago`;
  // The DATE is the point, so it leads and it is never relative-only: "3 days
  // ago" with no date makes a reader work out when that was, and an undated
  // brief has to say so rather than print a plausible-looking blank.
  const ran = brief.generated_at ? new Date(brief.generated_at) : null;
  const stamp = ran && !isNaN(ran)
    ? ran.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) +
      " at " + ran.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : null;
  return (
    <div style={{
      background: stale ? "#fff8ec" : "#f2f6f2",
      border: `1px solid ${stale ? "#f0dcae" : "#dfe8df"}`,
      borderRadius: 10, padding: "10px 14px", marginBottom: 12,
      display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 8,
    }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: stale ? "#7a5a00" : "#2d4a2d" }}>
        {stale ? "⏳" : "🕒"} This {what} brief ran{" "}
        {stamp ? <strong>{stamp}</strong> : <strong>at an unrecorded time</strong>}
      </span>
      <span style={{ fontSize: 12.5, color: stale ? "#6a5a30" : "#5a7a5a" }}>
        ({age}){brief.as_of ? <> · from data as of <strong>{brief.as_of}</strong></> : null}
      </span>
      <span style={{ fontSize: 12, color: stale ? "#6a5a30" : "#60735f" }}>
        {/* vercel.json runs grain-/milk-/cattle-brief every weekday at
            06:00-06:20 UTC (~2 AM ET). This line said "on demand, not on a
            schedule" after those crons existed, which made a fresh brief read
            as a stale one. */}
        A new brief is written each weekday morning.
        {canRegenerate && stale ? " Regenerate above for today's market." : ""}
      </span>
    </div>
  );
}

function MilkMIView({ isMobile, mode = "admin", defaultState = "NY" }) {
  // Portal users can VIEW the latest cached brief (GET is public) but not
  // regenerate it — the POST is admin-gated at the API. Hide the button + retune
  // copy for portal. The brief itself is a non-PII market read, portal-safe.
  const isPortal = mode === "portal";
  const canGenerate = !isPortal && isCurrentUserAdmin();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);
  const [showData, setShowData] = useState(false);
  const [news, setNews] = useState(null); // null=loading, []=none, [...]=items
  const [state, setState] = useState(({ NY: "36", PA: "42", MI: "26" }[(defaultState || "NY").toUpperCase()]) || "36"); // 36=NY, 42=PA, 26=MI — fundamentals state

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`/api/market?fn=milk-brief&state=${state}`);
        const j = await asJson(r);
        if (alive) setBrief(j?.brief || null);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [state]);

  // Real dairy-market news (Google News RSS via /api/market?fn=dairy-news) — live links.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/market?fn=dairy-news");
        const j = await asJson(r);
        if (alive) setNews(j?.ok ? (j.items || []) : []);
      } catch { if (alive) setNews([]); }
    })();
    return () => { alive = false; };
  }, []);

  async function generate() {
    setGenerating(true);
    setErr(null);
    try {
      const token = localStorage.getItem("cloverag_access_token");
      const r = await fetch(`/api/market?fn=milk-brief&state=${state}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const j = await asJson(r);
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setBrief(j.brief);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setGenerating(false);
    }
  }

  const relTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  };

  const n = brief?.narrative || null;
  const card = (children, extra = {}) => (
    <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: isMobile ? 14 : 18, ...extra }}>{children}</div>
  );

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: isMobile ? "14px 4px" : "20px 0" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: isMobile ? 18 : 21, fontWeight: 700, color: "#1a2e1a" }}>
            🧠 Milk Market Intelligence
          </div>
          <div style={{ fontSize: 12, color: "#60735f" }}>
            {isPortal
              ? "An AI read of the milk market from our data — updated by your Clover Ag agent"
              : "AI read of the milk market from our data — generated on demand · internal only"}
          </div>
        </div>
        {canGenerate && (
          <button
            onClick={generate}
            disabled={generating}
            style={{
              padding: "10px 18px", fontSize: 14, fontWeight: 700, fontFamily: "'Lora','Georgia',serif",
              color: "#fff", background: generating ? "#60735f" : "#2d4a2d", border: "none", borderRadius: 8,
              cursor: generating ? "default" : "pointer", whiteSpace: "nowrap",
            }}
          >
            {generating ? "Analyzing milk markets…" : brief ? "↻ Regenerate" : "Generate brief"}
          </button>
        )}
      </div>

      {/* State fundamentals toggle — supply + FMMO pool are state-specific (Class prices are national) */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: 0.5 }}>Fundamentals</span>
        {[{ k: "36", label: "New York" }, { k: "42", label: "Pennsylvania" }, { k: "26", label: "Michigan" }].map(s => (
          <button key={s.k} onClick={() => setState(s.k)} disabled={generating}
            style={{ padding: "6px 14px", borderRadius: 8, border: state === s.k ? "2px solid #4a6e4a" : "1px solid #d0e0d0",
              background: state === s.k ? "#4a6e4a" : "#fff", color: state === s.k ? "#fff" : "#4a6e4a",
              fontWeight: 700, fontSize: 13, cursor: generating ? "default" : "pointer" }}>
            {s.label}
          </button>
        ))}
        <span style={{ fontSize: 11, color: "#677167", marginLeft: 4 }}>{state === "26" ? "Mideast FMMO 33" : "Northeast FMMO 1"}</span>
      </div>

      {/* Dairy market news — real, current links from Google News RSS (no AI, no fabrication) */}
      <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: isMobile ? 14 : 18, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 15, fontWeight: 700, color: "#1a2e1a" }}>📰 Dairy Market News</div>
          <div style={{ fontSize: 11, color: "#677167" }}>US markets · live headlines</div>
        </div>
        {news === null ? (
          <div style={{ fontSize: 13, color: "#60735f" }}>Loading headlines…</div>
        ) : news.length === 0 ? (
          <div style={{ fontSize: 13, color: "#60735f" }}>No headlines available right now.</div>
        ) : (
          <div>
            {news.map((it, i) => (
              <div key={i} style={{ padding: "9px 0", borderTop: i ? "1px solid #f0f4f0" : "none" }}>
                <a href={it.link} target="_blank" rel="noopener noreferrer"
                   style={{ fontSize: 13.5, fontWeight: 600, color: "#2d5a10", textDecoration: "none", lineHeight: 1.4 }}>
                  {it.title}
                </a>
                <div style={{ fontSize: 11, color: "#677167", marginTop: 2 }}>
                  {it.source || "—"}{it.pubDate ? ` · ${new Date(it.pubDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {generating && (
        <div style={{ fontSize: 13, color: "#4a6e4a", marginBottom: 12, fontStyle: "italic" }}>
          Reading Class III/IV prices, the forward curve, FMMO pool data and NY supply… this takes ~15–25 seconds.
        </div>
      )}
      {err && (
        <div style={{ background: "#fdecea", border: "1px solid #f5c6c0", borderRadius: 8, padding: 12, color: "#a3372d", fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      )}

      {loading && !brief && <div style={{ color: "#60735f", fontSize: 14 }}>Loading latest brief…</div>}

      {!loading && !brief && !generating && (
        <div style={{ ...{ background: "#f2f6f2", border: "1px dashed #c3d4c3", borderRadius: 10 }, padding: 28, textAlign: "center", color: "#4a5a4a" }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>🥛</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>No brief yet</div>
          <div style={{ fontSize: 13, color: "#60735f" }}>Hit “Generate brief” to have the agent tell you what’s going on in milk markets.</div>
        </div>
      )}

      {n && <BriefRunDate brief={brief} canRegenerate={canGenerate} what="milk market" />}
      {n && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 12, color: "#60735f" }}>
            Data as of <strong>{brief.as_of || "—"}</strong> · generated {relTime(brief.generated_at)}
            {brief.model && isCurrentUserAdmin() ? ` · ${brief.model}` : ""}
          </div>

          {n.headline && card(
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#1a2e1a", lineHeight: 1.45, fontFamily: "'Lora','Georgia',serif" }}>
              {n.headline}
            </div>,
            { borderLeft: "4px solid #4a6e4a" }
          )}

          {Array.isArray(n.sections) && n.sections.map((s, i) => card(
            <div key={i}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#2d4a2d", marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 14, color: "#3a4a3a", lineHeight: 1.6 }}>{s.body}</div>
            </div>
          ))}

          {n.drpTakeaway && card(
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1a4a1a", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>💰 DRP Takeaway</div>
              <div style={{ fontSize: 14, color: "#1a3a1a", lineHeight: 1.6 }}>{n.drpTakeaway}</div>
            </div>,
            { background: "#eaf3ea", border: "1px solid #cfe3cf" }
          )}

          {n.teaching && card(
            <div style={{ fontSize: 13.5, color: "#6a5a2a", lineHeight: 1.6, fontStyle: "italic" }}>
              <strong style={{ fontStyle: "normal" }}>📘 Learn:</strong> {n.teaching}
            </div>,
            { background: "#fbf6e8", border: "1px solid #ece2c4" }
          )}

          {Array.isArray(n.watch) && n.watch.length > 0 && card(
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#2d4a2d", marginBottom: 6 }}>👀 What to watch</div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: "#3a4a3a", lineHeight: 1.7 }}>
                {n.watch.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div>
            <button onClick={() => setShowData(v => !v)} style={{ background: "none", border: "none", color: "#60735f", fontSize: 12, cursor: "pointer", padding: "4px 0", textDecoration: "underline" }}>
              {showData ? "Hide" : "Show"} the data the agent read
            </button>
            {showData && (
              <pre style={{ background: "#1a2e1a", color: "#cfe3cf", padding: 14, borderRadius: 8, fontSize: 11, overflowX: "auto", lineHeight: 1.5, marginTop: 6 }}>
                {(() => { try { return renderSnapshotText(brief.snapshot); } catch { return JSON.stringify(brief.snapshot, null, 2); } })()}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Compact client-side render of the stored snapshot (mirrors the server data block)
function renderSnapshotText(s) {
  if (!s) return "(no snapshot stored)";
  const f2 = (x) => (x == null ? "—" : Number(x).toFixed(2));
  const L = [];
  L.push(`MILK SNAPSHOT — as of ${s.asOf || "n/a"}`);
  if (s.announced) {
    L.push("\nAnnounced Class prices ($/cwt):");
    for (const cls of ["Class III", "Class IV"]) {
      const arr = s.announced[cls] || [];
      L.push(`  ${cls}: ${arr.map(r => `${r.ym} $${f2(r.price)}`).join("  ")}`);
    }
  }
  if (Array.isArray(s.curve)) {
    L.push("\nForward curve / DRP net floors (95%, PF 1.0):");
    for (const c of s.curve) {
      L.push(`  ${c.quarter} ${c.milk_class}: exp $${f2(c.expPrice)}, net floor $${f2(c.netFloor)} (1wk ${c.dWk == null ? "—" : (c.dWk >= 0 ? "+" : "") + c.dWk}, %ile ${c.pctile12 == null ? "—" : c.pctile12 + "%"})`);
    }
  }
  if (Array.isArray(s.ppd)) {
    L.push("\nFMMO pool:");
    for (const p of s.ppd) L.push(`  ${p.ym} ${p.order || ""}: SUP $${f2(p.sup)}, PPD $${f2(p.ppd)}`);
  }
  if (s.supply) {
    L.push("\nNY supply (NASS):");
    for (const [k, o] of Object.entries(s.supply)) {
      if (o) L.push(`  ${k}: ${o.value} ${o.unit || ""} (${o.period})${o.yoyPct == null ? "" : `, YoY ${o.yoyPct >= 0 ? "+" : ""}${o.yoyPct}%`}`);
    }
  }
  return L.join("\n");
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// DRP TABBED VIEW — Consolidates Policies, Quoting, Emails, Performance
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// DRP Buy-Signal — experimental, ADMIN-ONLY conditions score (v1)
//
// Answers ONE question: "from a risk-management standpoint, how attractive is it
// to lock a DRP floor for this quarter RIGHT NOW?" — NOT a price forecast. It
// scores asymmetry, not direction. Backtested on this DB 2026-06-29 (14yr settled
// Class III/IV): when milk sits in the top 20% of its trailing 2yr range it fell
// over the next 6mo 74% of the time (87% if already rolling over); cheap milk rose
// 74%. See memory project_drp_buy_signal_backtest for full method + caveats.
//
// Transparent weighted composite so every number is explainable. Score = signal
// strength (conditions) only; confidence is reported SEPARATELY as a trust /
// liquidity indicator — not multiplied into the score (validated 2026-07-07,
// tools/drp-signal/backtest.js: the confidence multiplier adds zero ranking skill
// and just collapsed far quarters toward neutral 5).
// ─────────────────────────────────────────────────────────────────────────────

// Seasonal forward-6mo bias by PURCHASE month, pooled Class III+IV, 2012-2026
// (avg $/cwt change over the next 6 months). Negative = milk tends to FALL after
// locking that month = favorable to lock. Source: backtest query 2026-06-29.
const DRP_SEASONAL_FWD6 = { 1:0.17, 2:0.31, 3:0.55, 4:0.80, 5:0.83, 6:0.25, 7:0.02, 8:-0.17, 9:-0.54, 10:-0.97, 11:-0.95, 12:-0.13 };
const DRP_SEASONAL_MAXABS = 0.97;
const DRP_W = { level: 0.45, seasonal: 0.20, margin: 0.15, positioning: 0.20 };
const drpClamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// English ordinal: 1→"1st", 2→"2nd", 3→"3rd", 11→"11th", 23→"23rd", etc.
// ⚠ ONE ORDINAL, BECAUSE FOUR COPIES MEANT TWO OF THEM WERE "th". The Cash
// Board printed a basis percentile as "72th" — 67th was right only by luck —
// while two correct implementations of this exact rule already existed twenty
// thousand lines apart. 1st / 2nd / 3rd / 11th / 12th / 13th / 21st / 72nd.
const ordinal = (n) => {
  const v = Math.abs(Math.round(Number(n) || 0)) % 100;
  const s = ["th", "st", "nd", "rd"];
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
const drpOrd = (n) => ordinal(n);
// Canonical quarter key (year+q) so the 2026-03-19 board label switch
// (Q3-27 → 2027-Q3) doesn't split a quarter's execution-window history.
// Canonical quarter key comes from the shared rule. Two copies of it is how the
// 2026-03-19 board relabel (Q3-27 -> 2027-Q3) splits a quarter's history in half.
const drpQKey = drpQKeyRule;

// Market regime = class-wide signal (same for every quarter of a class), derived
// from the trailing settled-price distribution. Youth-independent by construction.
// NOTE: scoring MUST stay in sync with api/_drp-signal.js (server snapshot writer).
function computeDrpRegime(classPrices, milkClass, purchaseMonth, dmcMargins, cotRows) {
  const series = (classPrices || [])
    .filter(r => r.milk_class === milkClass && r.price != null)
    .map(r => ({ ym: r.year * 12 + (r.month - 1), y: r.year, m: r.month, price: Number(r.price) }))
    .sort((a, b) => a.ym - b.ym);
  if (series.length < 12) return null;
  const latest = series[series.length - 1];
  // Trailing-24mo SETTLED distribution — the forward lockable price is ranked
  // against this per quarter (level computed in scoreDrpQuarter, forward-looking).
  const settledWin = series.slice(-24).map(p => p.price);
  const prior3 = series.length >= 4 ? series[series.length - 4] : null;
  const mom = prior3 ? latest.price - prior3.price : null;        // 3-mo regime momentum
  const seasAvg = DRP_SEASONAL_FWD6[purchaseMonth] ?? 0;
  const seasonal = drpClamp(-seasAvg / DRP_SEASONAL_MAXABS, -1, 1);
  // DMC margin pressure: high margin → herd expansion → bearish lag → favors locking
  let marginAvail = false, marginPctile = null, marginVal = null, margin = 0;
  const ms = (dmcMargins || []).filter(r => r.margin != null)
    .map(r => ({ ym: r.year * 12 + (r.month - 1), v: Number(r.margin) })).sort((a, b) => a.ym - b.ym);
  if (ms.length >= 12) {
    const lm = ms[ms.length - 1], mwin = ms.slice(-24);
    marginPctile = mwin.filter(p => p.v <= lm.v).length / mwin.length;
    marginVal = lm.v; margin = drpClamp((marginPctile - 0.5) * 2, -1, 1); marginAvail = true;
  }
  // CFTC managed-money positioning (Class III only): crowded net-long = contrarian
  // bearish = favors locking. Verified source: CFTC Disaggregated 72hh-3qpy, code 052641.
  let posAvail = false, posPctile = null, posNet = null, positioning = 0;
  if (milkClass === "III" && cotRows && cotRows.length) {
    const cs = cotRows.filter(r => r.mm_net != null)
      .map(r => ({ d: r.report_date, v: Number(r.mm_net) })).sort((a, b) => (a.d < b.d ? -1 : 1));
    if (cs.length >= 26) {
      const lc = cs[cs.length - 1], cwin = cs.slice(-156);       // ~3yr trailing
      posPctile = cwin.filter(p => p.v <= lc.v).length / cwin.length;
      posNet = lc.v; positioning = drpClamp((posPctile - 0.5) * 2, -1, 1); posAvail = true;
    }
  }
  return { latestPrice: latest.price, latestY: latest.y, latestM: latest.m, settledWin,
    mom, seasAvg, seasonal, marginAvail, marginPctile, marginVal, margin,
    posAvail, posPctile, posNet, positioning };
}

// Per-quarter score. LEVEL is forward-looking: rank the quarter's LOCKABLE forward
// price (fwdPrice, from drp_board) against the 2yr settled distribution — not the
// settled spot. conditions ∈ [-1,1]; score = clamp(5 + 5·conditions·confidence, 1, 10).
function scoreDrpQuarter(regime, { fwdPrice, horizonQ, windowQuotes, stale, execPctile }) {
  const haveMargin = regime.marginAvail;
  const w = regime.settledWin || [];
  const pctile = (fwdPrice != null && w.length) ? w.filter(p => p <= fwdPrice).length / w.length : 0.5;
  const level = drpClamp((pctile - 0.5) * 2, -1, 1);
  let num = DRP_W.level * level + DRP_W.seasonal * regime.seasonal;
  let den = DRP_W.level + DRP_W.seasonal;
  if (haveMargin) { num += DRP_W.margin * regime.margin; den += DRP_W.margin; }
  if (regime.posAvail) { num += DRP_W.positioning * regime.positioning; den += DRP_W.positioning; }
  const base = num / den;
  // momentum interaction (confirmation kicker + anti-blowoff penalty)
  const mom = regime.mom, momAvail = mom != null;
  let kicker = 0, penalty = 0;
  if (momAvail) {
    if (level > 0.2 && mom < 0) kicker = 0.25 * level * drpClamp(Math.abs(mom) / 2, 0, 1);
    if (pctile >= 0.9 && mom > 2) penalty = 0.30 * drpClamp((mom - 2) / 2, 0, 1);
  }
  const conditions = drpClamp(base + kicker - penalty, -1, 1);
  // confidence stack — a TRUST / liquidity indicator, reported in its own column
  const cWindow = 1;                                              // 14yr settled history
  const cMom = momAvail ? 1 : 0.85;
  let cLiq = horizonQ <= 2 ? 1 : horizonQ === 3 ? 0.85 : horizonQ === 4 ? 0.70 : 0.60;
  if (stale) cLiq *= 0.8;
  const cCov = 0.85 + 0.15 * den;                                // den = available-weight sum (max 1.0)
  const confidence = drpClamp(cWindow * cMom * cLiq * cCov, 0, 1);
  // Score reflects SIGNAL STRENGTH (conditions) only — confidence is shown
  // separately, NOT multiplied in. Folding it into the score double-counted the
  // horizon discount (already its own column) and collapsed every far quarter
  // toward neutral 5. Backtest (tools/drp-signal/backtest.js): the confidence
  // multiplier adds ZERO ranking skill — score-tercile spread == conditions-
  // tercile spread in every window. conditions is the skillful quantity.
  const score = drpClamp(5 + 5 * conditions, 1, 10);
  return {
    base, kicker, penalty, conditions, confidence, score, level, pctile, fwdPrice,
    contrib: {
      level: (DRP_W.level * level) / den,
      seasonal: (DRP_W.seasonal * regime.seasonal) / den,
      margin: haveMargin ? (DRP_W.margin * regime.margin) / den : null,
      positioning: regime.posAvail ? (DRP_W.positioning * regime.positioning) / den : null,
    },
    execPctile: (windowQuotes >= 8 && execPctile != null) ? execPctile : null,
  };
}

// Quarter parsing lives in src/drpDecision.js. Two copies of this regex is how
// the 2026-03-19 board relabel (Q3-27 -> 2027-Q3) splits a quarter's history.
const parseDrpQuarter = drpParseQuarterRule;

// drpQuartersForState — the per-state DRP market model (regime + per-quarter
// score for both classes), extracted from DRPSignalView's own useMemo so a
// second caller gets the SAME assembly of
// horizonQ/windowQuotes/execPctile instead of a hand-copy that could drift.
// Pure — no fetch. `board` is one state's drp_board rows; classPrices/dmc/cot
// are book-wide (unscoped by state, matching DRPSignalView's own fetches).
// `prevByKey` (optional) maps `${III|IV}|${qkey}` to the newest PRIOR stored
// decision row, so the page applies the same START/WATCH hysteresis the
// snapshot writer does. Absent, there is no hysteresis — never a guessed state.
function drpQuartersForState(classPrices, board, dmc, cot, prevByKey) {
  if (!classPrices.length || !board.length) return null;
  const now = new Date();
  const curMonth = now.getMonth() + 1, curYear = now.getFullYear();
  const curQ = Math.floor(now.getMonth() / 3) + 1;
  const globalLatest = board.reduce((mx, r) => r.price_date > mx ? r.price_date : mx, board[0].price_date);
  const out = {};
  for (const [drpCls, cpCls] of [["Class III", "III"], ["Class IV", "IV"]]) {
    const regime = computeDrpRegime(classPrices, cpCls, curMonth, dmc, cot);
    if (!regime) { out[cpCls] = null; continue; }
    const todays = board.filter(r => r.milk_class === drpCls && r.price_date === globalLatest);
    const quarters = todays.map(r => {
      const pq = parseDrpQuarter(r.quarter);
      const horizonQ = pq ? (pq.year * 4 + (pq.q - 1)) - (curYear * 4 + (curQ - 1)) : 99;
      // An unreadable label groups under its own literal text, never with every
      // other unreadable label — drpQKey returns null for those by design.
      const rKey = drpQKey(r.quarter) || r.quarter;
      const hist = board.filter(b => b.milk_class === drpCls && (drpQKey(b.quarter) || b.quarter) === rKey && b.net_floor != null);
      const windowQuotes = new Set(hist.map(b => b.price_date)).size;
      const nf = r.net_floor != null ? Number(r.net_floor) : null;
      const fwd = r.price != null ? Number(r.price) : null;
      const execPctile = (nf != null && hist.length)
        ? hist.filter(b => Number(b.net_floor) <= nf).length / hist.length : null;
      const sc = scoreDrpQuarter(regime, { fwdPrice: fwd, horizonQ, windowQuotes, stale: false, execPctile });
      // THE DECISION. Built from facts about this state x class x quarter only —
      // it is handed no regime, so no class-wide factor can reach it. Same
      // function, same history builder, as the snapshot writer and the backtest.
      const fh = drpFloorHistory(board, { state: null, milkClass: drpCls, quarter: r.quarter, asOf: globalLatest });
      // ⚠ asOf IS THE CLOCK, boardDate IS THE DATA. Passing globalLatest for both
      // — which is what shipped — made boardAgeDays structurally 0 and the
      // staleness gate unreachable, so Friday's board rendered as a current,
      // actionable read on Saturday, Sunday and Monday.
      const decision = drpDecideRule({
        previousDecision: drpPreviousDecisionInput(prevByKey ? prevByKey[`${cpCls}|${drpQKey(r.quarter)}`] : null),
        asOf: drpTodayIso(), boardDate: globalLatest, nowUtcHour: new Date().getUTCHours(),
        milkClass: drpCls, quarter: r.quarter,
        fwdPrice: fwd, floor: r.floor != null ? Number(r.floor) : null,
        premium: r.premium != null ? Number(r.premium) : null, netFloor: nf,
        history: fh.byDate, historyError: fh.error,
      });
      return {
        quarter: r.quarter, pq, horizonQ, windowQuotes,
        floor: r.floor != null ? Number(r.floor) : null,
        premium: r.premium != null ? Number(r.premium) : null,
        netFloor: nf, ...sc, decision,
      };
    }).filter(x => x.horizonQ >= 0)
      .sort((a, b) => a.horizonQ - b.horizonQ);
    out[cpCls] = { regime, quarters };
  }
  return { globalLatest, out };
}

// Forward track record: the nearest open Class III quarter's score over time,
// logged daily server-side (drp_signal_daily). Empty until snapshots accrue.
function SignalHistory({ history, model, isMobile }) {
  const nearest = model?.out?.III?.quarters?.[0]?.quarter || null;
  const rows = (history || [])
    .filter(r => r.milk_class === "III" && (!nearest || r.quarter === nearest) && r.score != null)
    .sort((a, b) => (a.snapshot_date < b.snapshot_date ? -1 : 1));
  const box = (children) => (
    <div style={{ background: "#fafdfa", border: "1px solid #e0eae0", borderRadius: 10, padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#2d4a2d", marginBottom: 6 }}>
        Signal history{nearest ? ` — Class III ${nearest}` : ""}
      </div>
      {children}
    </div>
  );
  if (rows.length < 2) {
    return box(<div style={{ fontSize: 12.5, color: "#60735f" }}>
      The track record starts accumulating now — a snapshot is logged daily, server-side. Once a few days build up, this charts how the score moved and what locking on a given day would have looked like.
      {rows.length === 1 && <span> (1 snapshot so far, {rows[0].snapshot_date}.)</span>}
    </div>);
  }
  const W = 720, H = 130, padL = 26, padR = 10, padT = 12, padB = 22;
  const x = (i) => padL + (i * (W - padL - padR)) / (rows.length - 1);
  const y = (v) => padT + ((10 - v) * (H - padT - padB)) / 10;
  const color = (s) => s >= 8 ? "#1a6e1a" : s >= 6.5 ? "#4a6e4a" : s >= 4.5 ? "#9a7d0a" : s >= 3 ? "#b06a2a" : "#8a6a6a";
  const linePts = rows.map((r, i) => `${x(i)},${y(Number(r.score))}`).join(" ");
  const last = rows[rows.length - 1];
  return box(
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 420, height: "auto", display: "block" }}>
        {[2, 5, 8].map(t => (
          <g key={t}>
            <line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke={t === 5 ? "#e2cf9a" : "#eef3ee"} strokeWidth="1" strokeDasharray={t === 5 ? "4 3" : undefined} />
            <text x={padL - 5} y={y(t) + 3} textAnchor="end" fontSize="9" fill="#677167">{t}</text>
          </g>
        ))}
        <polyline points={linePts} fill="none" stroke="#4a6e4a" strokeWidth="1.5" />
        {rows.map((r, i) => <circle key={i} cx={x(i)} cy={y(Number(r.score))} r="2.6" fill={color(Number(r.score))} />)}
        {[0, rows.length - 1].map(i => (
          <text key={i} x={x(i)} y={H - 6} textAnchor={i === 0 ? "start" : "end"} fontSize="9" fill="#677167">{rows[i].snapshot_date}</text>
        ))}
      </svg>
      <div style={{ fontSize: 11, color: "#60735f", marginTop: 4 }}>
        latest {Number(last.score).toFixed(1)} ({last.snapshot_date}) · dashed line = neutral 5 · {rows.length} snapshots
      </div>
    </div>
  );
}

function DRPSignalView({ isMobile }) {
  const [state, setState] = useState("36");                       // 36=NY 42=PA 26=MI
  const [classPrices, setClassPrices] = useState([]);
  const [dmc, setDmc] = useState([]);
  const [cot, setCot] = useState([]);
  const [board, setBoard] = useState([]);
  // Existing DRP coverage across the book, per canonical quarter x class.
  // `null` means "not loaded yet"; an empty map means "loaded, none found",
  // and those are different answers.
  const [endorsements, setEndorsements] = useState(null);
  // policy_id -> farmer_id. `null` means the join is unavailable, which makes
  // the book line name POLICIES rather than silently calling policies dairies.
  const [policyFarmer, setPolicyFarmer] = useState(null);
  const [history, setHistory] = useState([]);
  // Stored drp_decision_daily rows — the prior state the hysteresis reads.
  const [storedDecisions, setStoredDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHow, setShowHow] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefErr, setBriefErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [cp, dm, co] = await Promise.all([
          sbFetch("class_prices", { select: "year,month,milk_class,price", order: "year.asc,month.asc,id.asc" }),
          sbFetch("dmc_margins", { select: "year,month,margin", order: "year.asc,month.asc" }),
          sbFetch("cot_dairy", { select: "report_date,mm_net", filters: ["contract_code=eq.052641"], order: "report_date.asc,id.asc" }).catch(() => []),
        ]);
        if (alive) { setClassPrices(cp || []); setDmc(dm || []); setCot(co || []); }
      } catch (e) { if (alive) setError(e.message); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 400);
        const b = await sbFetch("drp_board", {
          select: "price_date,quarter,milk_class,price,floor,premium,net_floor,state_code",
          filters: [`state_code=eq.${state}`, `price_date=gte.${cutoff.toISOString().slice(0, 10)}`],
          order: "price_date.asc",
        });
        if (alive) setBoard(b || []);
        // Existing coverage is a SEPARATE read and a separate failure: if it
        // errors we must show "coverage unknown", never "no coverage".
        try {
          const en = await sbFetch("drp_endorsements", {
            select: "policy_id,quarter,end_date,declared_milk_prod,coverage_level,protection_factor,class_iii_pct,liability,producer_premium",
            order: "quarter.asc,id.asc",
          });
          if (alive) setEndorsements(en || []);
          // ⚠ A POLICY IS NOT A DAIRY, AND FOR DRP THEY DIVERGE ON MOST FARMS.
          // The book line counted DISTINCT `policy_id` and called them farms.
          // Measured 2026-09-03: 6 of the 9 DRP farmers hold MORE THAN ONE DRP
          // policy, and on 5 of 19 quarters the policy count exceeds the farmer
          // count — Q4 2026 read "6 farms" against 5 real dairies. That is the
          // same defect Kevin caught one line up in a new costume: a number
          // labelled as something it is not.
          //
          // The farmer lives on `policies`, not on the endorsement, so it takes
          // its own read. It is its OWN failure: if this read fails the count
          // falls back to naming policies, which is what the data can still
          // support — never a dairy count derived from policies.
          try {
            const ids = [...new Set((en || []).map((x) => x.policy_id).filter(Boolean))];
            const pol = ids.length
              ? await sbFetch("policies", { select: "id,farmer_id", filters: [`id=in.(${ids.join(",")})`] })
              : [];
            if (alive) setPolicyFarmer(Object.fromEntries((pol || []).map((p) => [p.id, p.farmer_id])));
          } catch (_) { if (alive) setPolicyFarmer(null); }
        } catch (_) { if (alive) { setEndorsements([]); setPolicyFarmer(null); } }
      } catch (e) { if (alive) setError(e.message); }
    })();
    return () => { alive = false; };
  }, [state]);

  // latest AI brief + score history for this state (server-logged track record)
  useEffect(() => {
    let alive = true;
    setBrief(null); setBriefErr(null);
    (async () => {
      try {
        const [bR, hR] = await Promise.all([
          fetch(`/api/market?fn=drp-signal&action=brief&state=${state}`).then(x => x.json()).catch(() => null),
          fetch(`/api/market?fn=drp-signal&action=history&state=${state}`).then(x => x.json()).catch(() => null),
        ]);
        if (!alive) return;
        setBrief(bR?.brief || null);
        setHistory(hR?.rows || []);
        setStoredDecisions(hR?.decisions || []);
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, [state]);

  async function generateBrief() {
    setBriefLoading(true); setBriefErr(null);
    try {
      const token = localStorage.getItem("cloverag_access_token");
      const r = await fetch(`/api/market?fn=drp-signal&action=brief&state=${state}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const j = await asJson(r);
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setBrief(j.brief);
    } catch (e) { setBriefErr(String(e.message || e)); }
    finally { setBriefLoading(false); }
  }

  const now = new Date();
  const curMonth = now.getMonth() + 1, curYear = now.getFullYear();
  const curQ = Math.floor(now.getMonth() / 3) + 1;

  // Per (class) regime + per (quarter,class) score from the latest board
  // snapshot — drpQuartersForState() (this file, above DRPSignalView), so
  // a second caller gets the identical assembly.
  // The newest stored decision per cell BEFORE today (UTC — the writer's own
  // snapshot_date), so the live read carries the same hysteresis input the
  // writer gave it. Version matching happens inside previousDecisionInput.
  const prevByKey = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const out = {};
    for (const r of storedDecisions || []) {
      if (!r || !r.snapshot_date || r.snapshot_date >= today) continue;
      const k = `${r.milk_class}|${r.qkey}`;
      if (!out[k] || r.snapshot_date > out[k].snapshot_date) out[k] = r;
    }
    return out;
  }, [storedDecisions]);
  const model = useMemo(() => drpQuartersForState(classPrices, board, dmc, cot, prevByKey), [classPrices, dmc, cot, board, prevByKey]);

  // ⚠ ONE COUNT OF THE BOOK, READ BY BOTH SURFACES.
  // Keyed per canonical QUARTER, not per class: `class_iii_pct` is unstated on
  // 205 of 266 endorsements, so the book cannot be split between Class III and
  // Class IV for most of it. `null` means not loaded, which is not zero.
  const bookByQuarter = useMemo(() => {
    if (!endorsements) return null;
    // `unit` names WHAT the second number counts, so the label can never claim
    // more than the join supports. With the farmer map it is dairies; without
    // it, policies — and a policy is not a dairy on most of this book.
    const unit = policyFarmer ? "dairy" : "policy";
    const out = new Map();
    for (const e of endorsements) {
      const r = drpEndorsementQKey(e);
      if (!r.key) continue;
      if (!out.has(r.key)) out.set(r.key, { n: 0, holders: new Set(), lbs: 0 });
      const g = out.get(r.key);
      g.n += 1;
      if (e.policy_id) {
        // An unmapped policy still counts as its own holder rather than being
        // dropped: a partial join must not shorten the count.
        const h = policyFarmer ? (policyFarmer[e.policy_id] || `p:${e.policy_id}`) : e.policy_id;
        g.holders.add(h);
      }
      if (e.declared_milk_prod != null) g.lbs += Number(e.declared_milk_prod) || 0;
    }
    const flat = {};
    for (const [k, g] of out) flat[k] = { n: g.n, holders: g.holders.size, unit, lbs: g.lbs };
    return flat;
  }, [endorsements, policyFarmer]);

  // The first actionable cell on screen, rendered as the agent would get it.
  // A preview built from a cell nobody can see would be a different alert.
  const alertPreview = useMemo(() => {
    if (!model || !model.out) return null;
    for (const cls of ["III", "IV"]) {
      const data = model.out[cls];
      if (!data) continue;
      for (const q of data.quarters || []) {
        const d = q.decision;
        if (!d || d.action !== "REVIEW") continue;
        const bk = bookByQuarter
          ? (bookByQuarter[drpQKey(q.quarter) || ""]
             || { n: 0, holders: 0, unit: policyFarmer ? "dairy" : "policy", lbs: 0 })
          : null;
        return renderDrpAlert({
          transition: { kind: d.decision === "BUY" ? "enter_buy" : "enter_start", reason: d.why },
          decision: d, prev: null,
          coverage: bk ? { book: bk } : null,
          stateCode: state,
        });
      }
    }
    return null;
    // policyFarmer decides the unit WORD ("dairy" vs "policy") on the preview.
    // Latent today — bookByQuarter is memoised on it, so it rebuilds anyway — but
    // a preview "rendered as the agent would get it" must not depend on that
    // coincidence holding.
  }, [model, bookByQuarter, state, policyFarmer]);

  const STATES = [{ k: "36", label: "New York" }, { k: "42", label: "Pennsylvania" }, { k: "26", label: "Michigan" }];
  const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];


  // ══════════════════════════════════════════════════════════════════════════
  // THE SURFACE. Rebuilt around one question a reader should answer in seconds:
  // WHICH QUARTERS ARE WORTH A LOOK, and how good is the floor if I look?
  //
  // What was here before was a ten-column table per class, under four
  // paragraphs of caveat. Every number on it was true and the page still could
  // not be read — the reader had to hold "horizon-matched", "n=19 within ±1mo",
  // "PF 1.00 basis" and five band names in their head before the first row
  // meant anything. That is a spreadsheet, not an answer.
  //
  // The ENGINE is unchanged. Everything technical still exists and is still
  // exact; it moved behind one disclosure at the foot of the page. Nothing here
  // computes a decision — this file only renders what drpDecide already said.
  // ══════════════════════════════════════════════════════════════════════════

  // Five bands, one visual scale. A gauge because "ABOVE TYPICAL" and "TYPICAL"
  // are two words apart and four pixels apart is easier to tell at a glance.
  const QUALITY = {
    "STRONG":        { rank: 5, color: "#1a6e1a", plain: "Strong" },
    "ABOVE TYPICAL": { rank: 4, color: "#4a8a3a", plain: "Above typical" },
    "TYPICAL":       { rank: 3, color: "#9a7d0a", plain: "Typical" },
    "BELOW TYPICAL": { rank: 2, color: "#b06a2a", plain: "Below typical" },
    "WEAK":          { rank: 1, color: "#b0563a", plain: "Weak" },
  };
  const QualityGauge = ({ label, size = 1 }) => {
    const q = QUALITY[label];
    const h = size === 2 ? 8 : 6, w = size === 2 ? 22 : 16;
    return (
      <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{
            width: w, height: h, borderRadius: 2,
            background: q && i <= q.rank ? q.color : "#e4ebe4",
          }} />
        ))}
      </div>
    );
  };

  // ── PLAIN LANGUAGE, NOT A PERCENTILE ────────────────────────────────────
  // "100th percentile (n=19 within ±1mo of 1mo out)" is exact and unreadable.
  // "the best of 19 comparable offers" is the same fact. The percentile, the
  // band width and the horizon all still appear — in the technical section,
  // where somebody checking the work will look for them.
  const plainContext = (d2) => {
    if (!d2 || d2.pctile == null) return "not enough comparable offers yet";
    const n = d2.horizonBandN || d2.n;
    if (!n) return "no comparable offers yet";
    if (d2.pctile >= 0.999) return `the best of ${n} comparable offers`;
    if (d2.pctile <= 0.05) return `among the lowest of ${n} comparable offers`;
    return `better than ${Math.round(d2.pctile * n)} of ${n} comparable offers`;
  };

  // ⚠ THIS PAGE HAS NO FARM, SO IT CANNOT MAKE A COVERAGE CLAIM.
  //
  // It used to print "Already covered" / "No coverage on file" per cell. Those
  // are FARM-level verdicts and there is no farm here — the only thing behind
  // them was "somebody in the book holds this quarter", which reads to a human
  // as "this dairy is covered". Kevin caught it immediately: covered for who?
  //
  // What the data can honestly say without a farm is how much of the AGENCY'S
  // BOOK already sits on that quarter. Two further limits, both measured, and
  // both are why this is stated as a count rather than a status:
  //   - it is PER QUARTER, not per class. `class_iii_pct` is unstated on 205 of
  //     266 endorsements (Q4 2026: 3 stated, 15 unstated), so attributing the
  //     book to Class III vs IV is not possible for most of it.
  //   - it says nothing about whether any particular dairy is protected. That
  //     is Farm Fit, which is not built.
  const bookOn = (quarter) => {
    if (!bookByQuarter) return null;                    // not loaded is not zero
    const key = drpQKey(quarter);
    if (!key) return null;
    return bookByQuarter[key] || { n: 0, holders: 0, unit: policyFarmer ? "dairy" : "policy", lbs: 0 };
  };
  // ⚠ THE LABEL MUST NAME THE QUARTER, BECAUSE THE CARD NAMES A CLASS.
  // A bare "Book: 18 endorsements" sitting under a CLASS IV heading, with the
  // SAME 18 repeated on the Class III row below it, reads as 18-and-18 — i.e.
  // as a per-class count, which is the one thing this number is not. Saying
  // "this quarter" is what stops a correct figure being read as a wrong one,
  // and it is the wording drpAlertEmail already uses, so the two surfaces
  // cannot describe the same number differently.
  const BookLine = ({ quarter, align }) => {
    const b = bookOn(quarter);
    const unitTxt = b && b.unit === "dairy"
      ? `${b.holders} dair${b.holders === 1 ? "y" : "ies"}`
      : b ? `${b.holders} polic${b.holders === 1 ? "y" : "ies"}` : "";
    const txt = !b ? "Book, this quarter: not loaded"
      : b.n === 0 ? "Book, this quarter: nothing yet"
      : `Book, this quarter: ${b.n} endorsement${b.n === 1 ? "" : "s"} · ${unitTxt}`;
    return (
      <span style={{ fontSize: 12, color: "#60735f", textAlign: align || "left" }}>{txt}</span>
    );
  };

  const qLabel = (q) => String(q || "").replace("-", " ");

  // Every open cell, flattened — the page is about quarters, not about classes.
  const cells = useMemo(() => {
    if (!model || !model.out) return [];
    const out = [];
    for (const cls of ["III", "IV"]) {
      const data = model.out[cls];
      if (!data) continue;
      for (const q of data.quarters || []) {
        if (!q.decision) continue;
        out.push({ cls, q, d: q.decision });
      }
    }
    return out.sort((a, b) => a.q.horizonQ - b.q.horizonQ);
  }, [model]);

  const worth = cells.filter((c) => c.d.attention === "REVIEW");
  const monitor = cells.filter((c) => c.d.attention === "MONITOR");
  const rest = cells.filter((c) => c.d.attention !== "REVIEW" && c.d.attention !== "MONITOR");
  const boardHealth = cells.length ? cells[0].d.boardHealth : null;

  // ── one card ────────────────────────────────────────────────────────────
  const Card = ({ cls, q, d: dd, dim }) => {
    const qual = QUALITY[dd.floorQuality && dd.floorQuality.label] || null;
    return (
      <div style={{
        background: "#fff", border: "1px solid " + (dim ? "#e8eee8" : "#d3e3d3"),
        borderRadius: 12, padding: "14px 16px", opacity: dim ? 0.85 : 1,
        display: "flex", flexDirection: "column", gap: 10, minWidth: 0,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 17, fontWeight: 700, color: "#1a2e1a" }}>
            {qLabel(q.quarter)}
          </span>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#60735f", letterSpacing: 0.3 }}>CLASS {cls}</span>
        </div>

        <div>
          <div style={{ fontSize: 30, fontWeight: 800, color: "#1a2e1a", lineHeight: 1.05, letterSpacing: -0.5 }}>
            {dd.netFloor != null ? fmtD(dd.netFloor) : "—"}
          </div>
          <div style={{ fontSize: 11.5, color: "#677167" }}>net floor you can establish</div>
        </div>

        <div>
          <QualityGauge label={dd.floorQuality && dd.floorQuality.label} size={2} />
          <div style={{ fontSize: 13, fontWeight: 700, color: qual ? qual.color : "#677167", marginTop: 5 }}>
            {qual ? qual.plain : "No read"}
          </div>
          <div style={{ fontSize: 12, color: "#60735f" }}>{plainContext(dd)}</div>
        </div>

        <div style={{ borderTop: "1px solid #eef3ee", paddingTop: 9 }}>
          <BookLine quarter={q.quarter} />
        </div>
      </div>
    );
  };

  // ── one compact row, for the quarters that are not today's business ──────
  const Row = ({ cls, q, d: dd }) => {
    const qual = QUALITY[dd.floorQuality && dd.floorQuality.label] || null;
    return (
      <div style={{
        display: "grid", gridTemplateColumns: isMobile ? "1fr auto" : "120px 74px 90px 1fr auto",
        gap: 10, alignItems: "center", padding: "9px 4px", borderTop: "1px solid #f0f4f0", fontSize: 13,
      }}>
        <span style={{ color: "#1a2e1a", fontWeight: 600 }}>
          {qLabel(q.quarter)} <span style={{ color: "#677167", fontWeight: 400 }}>· Cl {cls}</span>
        </span>
        {!isMobile && <b style={{ color: "#3a4a3a" }}>{dd.netFloor != null ? fmtD(dd.netFloor) : "—"}</b>}
        {!isMobile && <QualityGauge label={dd.floorQuality && dd.floorQuality.label} />}
        {!isMobile && <span style={{ color: qual ? qual.color : "#677167" }}>{qual ? qual.plain : "No read"}</span>}
        <span style={{ textAlign: "right" }}><BookLine quarter={q.quarter} align="right" /></span>
      </div>
    );
  };

  const Section = ({ title, count, children }) => (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: "#60735f", marginBottom: 10 }}>
        {title}{count != null ? ` · ${count}` : ""}
      </div>
      {children}
    </div>
  );

  const grid = {
    display: "grid", gap: 12,
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(230px, 1fr))",
  };

  if (loading) return <div style={{ padding: 24, color: "#60735f" }}>Loading the board…</div>;
  if (error) return <div style={{ padding: 24, color: "#b0563a" }}>Couldn't load: {error}</div>;

  // ── the headline: the answer, in one sentence ───────────────────────────
  const headline = !cells.length ? "No open quarters quoted for this state."
    : worth.length ? `${worth.length} quarter${worth.length === 1 ? "" : "s"} worth reviewing`
    : monitor.length ? `Nothing to act on — ${monitor.length} worth watching`
    : "Nothing worth reviewing today";
  const subline = !cells.length ? ""
    : worth.length
      ? (() => {
          const cs = Array.from(new Set(worth.map((c) => "Class " + c.cls)));
          return `${cs.join(" and ")} — strong floors for this point in the quarter.`;
        })()
      : "Every open quarter is at or below its usual floor for this stage.";

  return (
    <div style={{ paddingTop: 18, maxWidth: 1080 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <span style={{ fontSize: 10.5, fontWeight: 800, color: "#9a6a00", background: "#ffe9bf", borderRadius: 5, padding: "2px 8px", letterSpacing: 0.4 }}>ADMIN ONLY · EXPERIMENTAL</span>
        <span style={{ fontSize: 11.5, color: "#8a6a2a" }}>Not visible to farmers. Not a price forecast.</span>
      </div>

      <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 20, fontWeight: 700, color: "#1a2e1a" }}>DRP Floor Quality</div>
      <div style={{ fontSize: 13, color: "#60735f", marginTop: 2 }}>
        Which quarters are worth a look, and how good the floor is if you look.
      </div>

      {/* state + board freshness, together, because one qualifies the other */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        {STATES.map((s) => (
          <button key={s.k} onClick={() => setState(s.k)}
            style={{ padding: "6px 14px", borderRadius: 8, border: state === s.k ? "2px solid #4a6e4a" : "1px solid #d0e0d0",
              background: state === s.k ? "#4a6e4a" : "#fff", color: state === s.k ? "#fff" : "#4a6e4a",
              fontWeight: 700, fontSize: 13, cursor: "pointer" }}>{s.label}</button>
        ))}
        {boardHealth && (
          <span style={{ fontSize: 12, color: boardHealth.state === "CURRENT" ? "#2d6a1a" : "#b06a2a", marginLeft: 4 }}>
            {boardHealth.state === "CURRENT" ? "✓" : "⚠"} board {model && model.globalLatest}
            {boardHealth.state !== "CURRENT" ? ` · ${boardHealth.state.toLowerCase()}` : ""}
          </span>
        )}
      </div>

      {/* ── THE ANSWER ─────────────────────────────────────────────────── */}
      <div style={{ marginTop: 18, background: worth.length ? "#f2f8f2" : "#f7f9f7",
        border: "1px solid " + (worth.length ? "#cfe4cf" : "#e6ece6"), borderRadius: 12, padding: "16px 18px" }}>
        <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: isMobile ? 20 : 24, fontWeight: 700, color: "#1a2e1a" }}>
          {headline}
        </div>
        {subline && <div style={{ fontSize: 13, color: "#5a6e5a", marginTop: 4 }}>{subline}</div>}
      </div>

      {/* ⚠ ATTENTION IS SUPPRESSED, NOT MISSING, WHEN THE BOARD IS NOT CURRENT.
          Saying "nothing to review" on a stale board would be a lie of omission:
          the engine withheld the read, it did not find nothing. */}
      {boardHealth && boardHealth.state !== "CURRENT" && boardHealth.state !== "DELAYED" && (
        <div style={{ marginTop: 10, background: "#fdecea", border: "1px solid #f3c9c1", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: "#8a3a2a" }}>
          <b>Today's read is withheld.</b> {boardHealth.reason}. The floors below are the last ones we hold, not today's.
        </div>
      )}

      {worth.length > 0 && (
        <Section title="WORTH REVIEWING" count={worth.length}>
          <div style={grid}>
            {worth.map((c) => <Card key={c.cls + c.q.quarter} {...c} />)}
          </div>
        </Section>
      )}

      {monitor.length > 0 && (
        <Section title="APPROACHING" count={monitor.length}>
          <div style={grid}>
            {monitor.map((c) => <Card key={c.cls + c.q.quarter} {...c} dim />)}
          </div>
        </Section>
      )}

      {rest.length > 0 && (
        <Section title="NOTHING TO RAISE TODAY" count={rest.length}>
          <div style={{ background: "#fff", border: "1px solid #e8eee8", borderRadius: 10, padding: "2px 12px 8px" }}>
            {rest.map((c) => <Row key={c.cls + c.q.quarter} {...c} />)}
          </div>
          <div style={{ fontSize: 11.5, color: "#677167", marginTop: 7 }}>
            A weak floor is a reason not to raise the quarter — not a reason a dairy should skip DRP.
          </div>
        </Section>
      )}

      {/* ── EVERYTHING TECHNICAL, BEHIND ONE DOOR ───────────────────────── */}
      <div style={{ marginTop: 26 }}>
        <button onClick={() => setShowHow(!showHow)}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
            fontSize: 12.5, color: "#4a6e4a", fontWeight: 700 }}>
          {showHow ? "▾" : "▸"} How this is worked out — method, sample, and the raw board
        </button>

        {showHow && (
          <div style={{ marginTop: 12, background: "#fbfdfb", border: "1px solid #e0eae0", borderRadius: 10, padding: "16px 18px", fontSize: 12.5, color: "#3a4a3a", lineHeight: 1.6 }}>

            <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>What is being compared</div>
            <p style={{ margin: "0 0 12px" }}>
              Each quarter is ranked on the <b>net floor</b> — the coverage floor minus the producer premium —
              for that exact state, class and quarter. It is compared only against offers quoted at a{" "}
              <b>similar horizon</b> (within ±1 month of today's months-to-close, widening to ±2 or ±3 only if
              fewer than 12 offers qualify). Producer premium decays as a quarter approaches, so ranking against
              a quarter's whole life would drift upward with age alone.
            </p>

            <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>When a quarter is raised first</div>
            <p style={{ margin: "0 0 12px" }}>
              A quarter is raised with <b>high priority</b> only when it is a <b>strong floor that has turned down
              from a recent high</b>: at or above the 75th percentile for its horizon, at least one typical
              five-day move below its own best of the last 20 pricing days, and not still climbing strongly.
              A strong floor that is still rising is raised at normal priority. Once raised, a quarter is not
              dropped to "monitor" until it falls 5 points below the strong band, so a rank wobbling around
              the line does not flip back and forth. All of these numbers are provisional.
            </p>

            <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>Comparison basis</div>
            <p style={{ margin: "0 0 12px" }}>
              Every figure is quoted at <b>95% coverage, protection factor 1.00</b> — the board convention, held
              fixed so quarters and dates stay comparable. It is <b>not</b> what the book buys: the agency's own
              endorsements average <b>PF 1.45</b>. A quote at a farm's own election belongs to Farm Fit, which is
              not built.
            </p>

            <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>What "Book" means on each card</div>
            <p style={{ margin: "0 0 12px" }}>
              This page is <b>not tied to any farm</b>, so it makes no claim about whether a dairy is protected.
              The Book line counts the <b>agency's own endorsements already sitting on that quarter</b> — nothing
              more. Two limits: it is <b>per quarter, not per class</b>, because <code>class_iii_pct</code> is
              unstated on <b>205 of 266</b> endorsements (Q4 2026: 3 stated, 15 unstated), so the book cannot be
              split between Class III and Class IV for most of it; and it says nothing about any individual
              dairy's exposure or existing coverage. That is Farm Fit, which is not built.
            </p>
            <p style={{ margin: "0 0 12px" }}>
              The second figure counts <b>dairies, not policies</b>, and the two genuinely differ here:
              <b> 6 of the 9 DRP farmers hold more than one DRP policy</b>, so on 5 of 19 quarters a
              distinct-policy count runs high (Q4 2026 is 5 dairies across 6 policies). The farmer sits on
              <code> policies</code>, not on the endorsement, so it takes its own read — and if that read
              fails the line says <b>policies</b> rather than calling policies dairies.
            </p>

            <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>Not used</div>
            <p style={{ margin: "0 0 12px" }}>
              Sales-window urgency — we hold no verified RMA sales-deadline source, so a nearing deadline never
              contributes. Check the closing date yourself. Class-wide market factors (seasonality, DMC margin,
              CFTC positioning) are context only and cannot raise a quarter.
            </p>

            <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>The raw board</div>
            <div style={{ overflowX: "auto", marginBottom: 12 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f2f6f2", textAlign: "left", color: "#2d4a2d" }}>
                    {["Quarter", "Cl", "Fwd", "Floor", "Premium", "Net floor", "Matched", "Life-to-date", "Sample", "Board", "Data"].map((h) => (
                      <th key={h} style={{ padding: "6px 8px", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cells.map(({ cls, q, d: dd }) => (
                    <tr key={"t" + cls + q.quarter} style={{ borderTop: "1px solid #eef3ee" }}>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{q.quarter}</td>
                      <td style={{ padding: "6px 8px" }}>{cls}</td>
                      <td style={{ padding: "6px 8px" }}>{q.fwdPrice != null ? fmtD(q.fwdPrice) : "—"}</td>
                      <td style={{ padding: "6px 8px" }}>{dd.floor != null ? fmtD(dd.floor) : "—"}</td>
                      <td style={{ padding: "6px 8px", color: "#b0563a" }}>{dd.premium != null ? "−" + fmtD(dd.premium) : "—"}</td>
                      <td style={{ padding: "6px 8px", fontWeight: 700 }}>{dd.netFloor != null ? fmtD(dd.netFloor) : "—"}</td>
                      <td style={{ padding: "6px 8px" }}>{dd.pctile != null ? drpOrd(Math.round(dd.pctile * 100)) : "—"}</td>
                      <td style={{ padding: "6px 8px", color: "#60735f" }}>{dd.rawPctile != null ? drpOrd(Math.round(dd.rawPctile * 100)) : "—"}</td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap", color: "#60735f" }}>
                        {dd.pctileBasis === "horizon-matched"
                          ? `n=${dd.horizonBandN} ±${dd.horizonBandMonths}mo @ ${dd.monthsOut}mo`
                          : `n=${dd.n} life-to-date`}
                      </td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap", color: "#60735f" }}>
                        {dd.boardDate || "—"}{dd.boardHealth && dd.boardHealth.state !== "CURRENT" ? " · " + dd.boardHealth.state.toLowerCase() : ""}
                      </td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap", color: "#60735f" }}>
                        {dd.dataQuality ? dd.dataQuality.status : "—"}{dd.usability && dd.usability !== "USABLE" ? " · " + dd.usability : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>Provenance</div>
            <p style={{ margin: "0 0 12px" }}>
              "Comparable offers" means offers <i>we captured</i>, not the whole historical DRP market. Capture
              begins <b>2025-01-10</b> for New York and <b>2026-06-24</b> for Pennsylvania and Michigan, so a
              PA/MI rank is taken over roughly two months of offers. Every row prints its own sample above.
              {cells[0] && cells[0].d.versions && (
                <> Rule version <b>{cells[0].d.versions.model_version}</b> / thresholds{" "}
                <b>{cells[0].d.versions.threshold_version}</b>.</>
              )}
            </p>

            {alertPreview && (
              <>
                <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>
                  Alert preview <span style={{ fontWeight: 600, color: "#9a6a00" }}>· delivery is off, nothing is sent</span>
                </div>
                <pre style={{ margin: 0, fontSize: isMobile ? 10.5 : 11.5, lineHeight: 1.5, color: "#3a4a3a",
                  whiteSpace: "pre", overflowX: "auto", background: "#fff", border: "1px solid #e8eee8",
                  borderRadius: 8, padding: "10px 12px",
                  fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}>{alertPreview.text}</pre>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// DRP PROTECTION PLANNER — farmer-facing "% protected" pacing tool.
// Leads with a disciplined pace (be X% protected by each milestone); the price
// signal is a LIGHT "good window to get ahead" nudge, NEVER a reason to wait.
// Validated on 93 quarters (tools/drp-signal/protection_sim.js): pacing is what
// protects the downside; the price trigger is a minor accelerant. Class III & IV
// are paced SEPARATELY (Kevin never blends the two routes).
// ─────────────────────────────────────────────────────────────────────────────
// % protected target by quarters-out. Moderately front-loaded (the 93-quarter
// backtest favors getting covered early over waiting), smooth/monotone, and you
// MUST be fully covered by the quarter's start (h0=100). A steady ~15-20%/quarter
// climb — no brittle steps.
const DRP_RAMP = { 0: 100, 1: 90, 2: 75, 3: 55, 4: 40, 5: 20 };
const drpRampTarget = (h) => (h <= 0 ? 100 : DRP_RAMP[h] ?? 20);
// On-pace TOLERANCE. A hard target line is brittle — 67% two quarters out reads as
// "behind" when it's genuinely fine. Anything within this band below target counts
// as on-pace; only quarters clearly under it flag as behind.
const DRP_PACE_BAND = 12;
const DRP_ST_FIPS = { NY: "36", PA: "42", MI: "26", MD: "24" };
const drpNormQ = (q) => { const m = /^Q([1-4])\s+(\d{4})$/.exec(String(q || "").trim()); return m ? `${m[2]}-Q${m[1]}` : String(q || ""); };

function DRPProtectionPlanner({ farmer, isMobile = false, drpEstProd = {}, allEnds = [], onPersist }) {
  const cls = "combined";   // pace recommendation is the combined III+IV position (routes shown per-quarter below)
  const [board, setBoard] = useState([]);
  const [cp, setCp] = useState([]); const [dmc, setDmc] = useState([]); const [cot, setCot] = useState([]);
  const [loading, setLoading] = useState(true);

  // best-effort state → FIPS for the board (defaults NY, the book's home state)
  const stFips = (() => {
    const cand = [farmer?.state, ...(farmer?.drpPolicies || []).map((p) => p.state), ...(farmer?.policies || []).map((p) => p.state)]
      .filter(Boolean).map((s) => String(s).toUpperCase());
    for (const s of cand) { if (DRP_ST_FIPS[s]) return DRP_ST_FIPS[s]; if (["36", "42", "26", "24"].includes(s)) return s; }
    return "36";
  })();

  useEffect(() => {
    let alive = true; setLoading(true);
    (async () => {
      try {
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
        const [a, b, c, bd] = await Promise.all([
          sbFetch("class_prices", { select: "year,month,milk_class,price", order: "year.asc,month.asc,id.asc" }),
          sbFetch("dmc_margins", { select: "year,month,margin", order: "year.asc,month.asc" }),
          sbFetch("cot_dairy", { select: "report_date,mm_net", filters: ["contract_code=eq.052641"], order: "report_date.asc,id.asc" }).catch(() => []),
          sbFetch("drp_board", { select: "price_date,quarter,milk_class,price,floor,premium,net_floor,state_code", filters: [`state_code=eq.${stFips}`, `price_date=gte.${cutoff.toISOString().slice(0, 10)}`], order: "price_date.asc" }),
        ]);
        if (!alive) return;
        setCp(a || []); setDmc(b || []); setCot(c || []); setBoard(bd || []); setLoading(false);
      } catch { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [stFips]);

  const now = new Date(); const curMonth = now.getMonth() + 1, curYear = now.getFullYear(), curQ = Math.floor(now.getMonth() / 3) + 1;

  // expected quarterly production (lbs) — persisted on the farmer's record; global
  // or per-quarter (shared one-to-one with the Summary tab's Est. Production).
  const cleanNum = (v) => { const n = Number(String(v ?? "").replace(/,/g, "")); return isFinite(n) && n > 0 ? n : null; };
  const perQ = drpEstProd?.__perQuarter === true;
  const boardToLabel = (bq) => { const m = /^(\d{4})-Q([1-4])$/.exec(bq); return m ? `Q${m[2]} ${m[1]}` : bq; };
  const estProdForQ = (bq) => (perQ ? cleanNum(drpEstProd?.[boardToLabel(bq)]) : cleanNum(drpEstProd?.__global));
  const anyProd = perQ
    ? Object.keys(drpEstProd || {}).some((k) => !k.startsWith("__") && cleanNum(drpEstProd[k]))
    : !!cleanNum(drpEstProd?.__global);
  const saveProd = (patch) => { if (onPersist) onPersist({ ...(drpEstProd || {}), ...patch }); };

  // locked production per quarter, per class. III & IV are kept SEPARATE (Kevin
  // never blends the two routes); the combined view only SUMS them for a total
  // position read (capped at 100% — see the note in the UI).
  const lockedByClassQ = useMemo(() => {
    const m = { III: {}, IV: {} };
    for (const e of allEnds) {
      const c = e.classIII ? "III" : "IV";
      const qk = drpNormQ(e.quarter);
      const shr = e.insShr != null ? Number(e.insShr) : 1;
      m[c][qk] = (m[c][qk] || 0) + (Number(e.declaredMilkProd) || 0) * (isFinite(shr) ? shr : 1);
    }
    return m;
  }, [allEnds]);

  const model = useMemo(() => {
    if (!cp.length || !board.length) return null;
    const regime = { III: computeDrpRegime(cp, "III", curMonth, dmc, cot), IV: computeDrpRegime(cp, "IV", curMonth, dmc, cot) };
    const latest = board.reduce((mx, r) => (r.price_date > mx ? r.price_date : mx), board[0].price_date);
    const rowsByQ = {};   // quarter -> { III: row, IV: row }
    board.filter((r) => r.price_date === latest).forEach((r) => {
      const c = r.milk_class === "Class III" ? "III" : r.milk_class === "Class IV" ? "IV" : null;
      if (!c) return; (rowsByQ[r.quarter] = rowsByQ[r.quarter] || {})[c] = r;
    });
    // Show the CURRENT in-progress quarter + future board quarters. The current
    // quarter (and any quarter the farmer already holds coverage in) is often OFF
    // the board — its sales window has closed — so union those in from the
    // endorsements. Pace logic (behind/catch-up) applies only to FUTURE, still-
    // lockable quarters; the current quarter is shown as their locked position.
    const curLabel = `${curYear}-Q${curQ}`;
    const heldQ = new Set();
    for (const c of ["III", "IV"]) for (const [q, lbs] of Object.entries(lockedByClassQ[c] || {})) if (lbs > 0) heldQ.add(q);
    const allQ = new Set([...Object.keys(rowsByQ), curLabel, ...heldQ]);
    const quarters = [...allQ].map((ql) => {
      const pq = parseDrpQuarter(ql);
      const horizonQ = pq ? (pq.year * 4 + (pq.q - 1)) - (curYear * 4 + (curQ - 1)) : 99;
      const onBoard = !!rowsByQ[ql];
      const actionable = onBoard && horizonQ >= 1;   // future & still lockable
      const target = drpRampTarget(horizonQ);
      const qEst = estProdForQ(ql);
      const per = {};
      for (const c of ["III", "IV"]) {
        const row = onBoard ? rowsByQ[ql][c] : null;
        const locked = (lockedByClassQ[c] || {})[ql] || 0;
        const sc = (row && regime[c]) ? scoreDrpQuarter(regime[c], { fwdPrice: row.price != null ? Number(row.price) : null, horizonQ, windowQuotes: 99, stale: false, execPctile: null }) : null;
        per[c] = { has: !!row, locked, netFloor: row && row.net_floor != null ? Number(row.net_floor) : null, premium: row && row.premium != null ? Number(row.premium) : null, pctile: sc ? sc.pctile : null, rich: !!(sc && sc.pctile != null && sc.pctile >= 0.75) };
      }
      const lockedSel = per.III.locked + per.IV.locked;
      const curPct = qEst ? Math.min(100, (lockedSel / qEst) * 100) : null;
      // pace band applies ONLY to still-lockable future quarters
      const behind = actionable && curPct != null && curPct < target - DRP_PACE_BAND;
      const onPace = actionable && curPct != null && !behind;
      const toTargetPct = behind ? Math.max(0, (target - DRP_PACE_BAND) - curPct) : 0;
      return {
        quarter: ql, horizonQ, target, curPct, toTargetPct, qEst, per, behind, onPace, onBoard, actionable,
        toTargetLbs: qEst && toTargetPct ? Math.round((toTargetPct / 100) * qEst) : 0,
        richAny: per.III.rich || per.IV.rich,
        richClasses: ["III", "IV"].filter((c) => per[c].rich),
      };
    }).filter((x) => x.horizonQ >= 0 && x.horizonQ <= 5).sort((a, b) => a.horizonQ - b.horizonQ);
    // pace aggregate + next-action are over FUTURE lockable quarters only
    const paced = quarters.filter((q) => q.actionable);
    let sumCur = 0, sumTgt = 0, n = 0;
    paced.forEach((q) => { if (q.curPct != null) { sumCur += q.curPct; sumTgt += q.target; n++; } });
    const aggCur = n ? sumCur / n : null, aggTgt = n ? sumTgt / n : null;
    const behindQs = paced.filter((q) => q.behind);
    const nextGap = behindQs.length ? behindQs[0] : null;   // sorted nearest-first already
    const totalCatchupLbs = behindQs.reduce((s, q) => s + (q.toTargetLbs || 0), 0);
    const status = aggCur == null ? "unset" : aggCur >= aggTgt - DRP_PACE_BAND ? (aggCur >= aggTgt + 3 ? "ahead" : "onpace") : "behind";
    return { latest, quarters, aggCur, aggTgt, status, nextGap, behindCount: behindQs.length, totalCatchupLbs, pacedCount: paced.length };
  }, [cp, dmc, cot, board, cls, lockedByClassQ, drpEstProd, curMonth, curYear, curQ]);

  const card = { background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: "14px 16px", marginBottom: 14 };
  const cap = { fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "#4a6e4a", textTransform: "uppercase", marginBottom: 8 };
  const PosBar = ({ cur, target, locked = false }) => {
    const bandLo = Math.max(0, target - DRP_PACE_BAND);
    const onPace = cur != null && cur >= bandLo;
    return (
      <div style={{ position: "relative", height: 20, flex: 1, minWidth: 110 }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: 7, height: 7, borderRadius: 4, background: "#eef3ee" }} />
        {/* on-pace band: bandLo → target (lighter green) — not shown for locked/current quarters */}
        {!locked && <div style={{ position: "absolute", left: `${bandLo}%`, width: `${Math.min(100, target) - bandLo}%`, top: 7, height: 7, background: "#d6e9db" }} title={`on-pace band ${Math.round(bandLo)}–${target}%`} />}
        {cur != null && <div style={{ position: "absolute", left: 0, top: 7, height: 7, borderRadius: 4, width: `${Math.min(100, cur)}%`, background: locked ? "#4a8a5f" : onPace ? "#2f8a48" : "#d99a2b" }} />}
        {!locked && <div style={{ position: "absolute", left: `${Math.min(100, target)}%`, top: 3, height: 15, width: 2, background: "#33402f", transform: "translateX(-1px)" }} title={`target ${target}%`} />}
      </div>
    );
  };

  return (
    <div style={{ paddingTop: 4 }}>
      {loading && <div style={{ color: "#60735f", padding: 20 }}>Loading…</div>}

      {!loading && !anyProd && (
        <div style={{ ...card, background: "#fff7e6", border: "1px solid #f0d9a0" }}>
          <div style={{ fontSize: 13, color: "#8a6a2a", marginBottom: 8 }}>Enter your expected milk production per quarter (lbs) to see your % protected — this saves to your record.</div>
          <input value={drpEstProd?.__global || ""} onChange={(e) => saveProd({ __global: e.target.value })} placeholder="e.g. 1,000,000"
            style={{ fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "1px solid #cfe0cf", width: 160 }} /> <span style={{ fontSize: 11, color: "#677167" }}>lbs / quarter</span>
        </div>
      )}

      {!loading && model && (<>
        {/* headline pace */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1a2e1a" }}>
              {model.aggCur != null ? `You're ${Math.round(model.aggCur)}% protected` : "Set production to see your pace"}
              <span style={{ fontWeight: 600, fontSize: 12, color: "#5c6f5c" }}> · Class III + IV · pace across {model.pacedCount} forward quarters</span>
            </div>
            <div style={{ fontSize: 11.5, color: "#5c6f5c", textAlign: "right" }}>
              {model.aggTgt != null && <span>plan pace ~{Math.round(model.aggTgt)}%</span>}
              <div style={{ fontSize: 10, color: "#677167" }}>board as of {model.latest}</div>
            </div>
          </div>
          {model.aggCur != null && <div style={{ margin: "10px 0 4px" }}><PosBar cur={model.aggCur} target={model.aggTgt} /></div>}
          {/* clear, single next action */}
          {model.aggCur != null && (() => {
            const s = model.status;
            const tone = s === "behind" ? { bg: "#fbf1e3", fg: "#b8760b", label: "Behind pace" } : s === "ahead" ? { bg: "#eaf6ec", fg: "#1f7a3d", label: "Ahead of pace" } : { bg: "#eaf6ec", fg: "#1f7a3d", label: "On pace" };
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                <span style={{ background: tone.bg, color: tone.fg, borderRadius: 7, padding: "3px 10px", fontSize: 12, fontWeight: 800 }}>{tone.label}</span>
                <span style={{ fontSize: 12.5, color: "#42563f" }}>
                  {model.behindCount > 0 && model.nextGap
                    ? <>Next: <b>{model.nextGap.quarter}</b>{model.behindCount > 1 ? ` +${model.behindCount - 1} more` : ""} under pace — protect <b>~{model.totalCatchupLbs.toLocaleString()} lbs</b> to catch up.</>
                    : <>All {model.pacedCount} forward quarters are inside the pace band — nothing needed right now.</>}
                </span>
              </div>
            );
          })()}
          <div style={{ fontSize: 11, color: "#60735f", marginTop: 8 }}>Getting covered steadily is what protects you — not timing the perfect price. The shaded band on each bar is the on-pace zone (nearer quarters sit to the right — they're held to a higher bar because the window to lock is closing). Only quarters clearly under the band need attention.</div>
        </div>

        {/* per-quarter pacing */}
        <div style={card}>
          <div style={cap}>Protection pace by quarter</div>
          {model.quarters.length === 0 && <div style={{ fontSize: 12, color: "#60735f" }}>No open quarters quoted for your state right now.</div>}
          {model.quarters.map((q) => (
            <div key={q.quarter} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 0", borderTop: "1px solid #eef3ee" }}>
              <div style={{ minWidth: 96 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a" }}>{q.quarter}</div>
                <div style={{ fontSize: 10.5, color: "#677167" }}>{q.horizonQ === 0 ? "current qtr" : `${q.horizonQ} qtr${q.horizonQ > 1 ? "s" : ""} out`}</div>
              </div>
              <div style={{ minWidth: 62, textAlign: "right" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: q.onPace ? "#1f7a3d" : q.behind ? "#b8860b" : q.curPct ? "#4a6e4a" : "#677167" }}>{q.curPct != null ? `${Math.round(q.curPct)}%` : "—"}</div>
                <div style={{ fontSize: 10, color: "#677167" }}>{q.actionable ? `target ${q.target}%` : "locked"}</div>
              </div>
              <PosBar cur={q.curPct} target={q.target} locked={!q.actionable} />
              <div style={{ minWidth: 104, textAlign: "right" }}>
                {q.onBoard ? (<>
                  {["III", "IV"].map((c) => (q.per[c] && q.per[c].has && q.per[c].netFloor != null) ? (
                    <div key={c} style={{ fontSize: 12, fontWeight: 700, color: "#2d4a2d" }}>
                      <span style={{ fontSize: 10.5, color: "#677167", fontWeight: 700 }}>{c} </span>{fmtD(q.per[c].netFloor)}
                    </div>
                  ) : null)}
                  <div style={{ fontSize: 10, color: "#677167" }}>net floor</div>
                </>) : (
                  <div style={{ fontSize: 10, color: "#677167" }}>{q.horizonQ === 0 ? "current · in progress" : "sales closed"}</div>
                )}
              </div>
              {perQ && (
                <input value={drpEstProd?.[boardToLabel(q.quarter)] || ""} onChange={(e) => saveProd({ [boardToLabel(q.quarter)]: e.target.value })} placeholder="lbs/qtr"
                  title="expected production this quarter" style={{ width: 92, fontSize: 11, padding: "3px 6px", borderRadius: 6, border: "1px solid #cfe0cf", textAlign: "right" }} />
              )}
              {q.richAny && (
                <span style={{ background: "#eaf6ec", color: "#1f7a3d", borderRadius: 7, padding: "3px 8px", fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}
                  title="Floor is in the top quartile of its 2-yr range — a good window to get ahead of pace">
                  {cls === "combined" && q.richClasses.length ? `${q.richClasses.join(" & ")} rich · get ahead` : "rich · good window to get ahead"}
                </span>
              )}
              {q.behind && q.toTargetPct > 0 && (
                <div style={{ fontSize: 11, color: "#b8760b", fontWeight: 600, flexBasis: isMobile ? "100%" : "auto" }}>
                  behind — protect ~<b>{Math.round(q.toTargetPct)}%</b>{q.toTargetLbs ? ` (${q.toTargetLbs.toLocaleString()} lbs)` : ""} to get on pace
                </div>
              )}
            </div>
          ))}
        </div>

        {/* production input + how it works */}
        <div style={card}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "#5c6f5c" }}>
            {!perQ && <label style={{ fontWeight: 700 }}>Expected production <input value={drpEstProd?.__global || ""} onChange={(e) => saveProd({ __global: e.target.value })} placeholder="lbs / qtr"
              style={{ width: 120, fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #cfe0cf", marginLeft: 4 }} /> lbs/qtr</label>}
            {perQ && <span style={{ fontSize: 11.5, fontWeight: 700 }}>Set production per quarter in the rows above.</span>}
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "pointer", userSelect: "none" }}>
              <input type="checkbox" checked={perQ} onChange={(e) => saveProd({ __perQuarter: e.target.checked })} /> enter per quarter
            </label>
            <span style={{ fontSize: 10.5, color: "#677167" }}>saves to your record</span>
          </div>
          <div style={{ fontSize: 11, color: "#60735f", marginTop: 10, lineHeight: 1.5 }}>
            Your <b>Class III + IV</b> coverage is summed against expected production (capped at 100%) — the two routes are always bought separately, but this paces your total pounds protected. Each quarter's row shows the live III and IV floors. The pace targets come from a 93-quarter backtest (2000–2026): getting covered <i>steadily</i> — building to full coverage by each quarter's start — lifted the worst‑case quarters by roughly <b>$0.85–1.60/cwt after premium</b> versus waiting, and beat trying to time the price. You're "on pace" anywhere inside the shaded band; only quarters clearly under it need attention. The <b>rich</b> flag is a green light to get <i>ahead</i> of pace when a lockable floor is historically high — never a reason to fall behind. Decision support, not a milk-price forecast.
          </div>
        </div>
      </>)}
    </div>
  );
}

// ── QUOTERS HUB — one page, a tab per quoter the user carries (scoped by lobs).
// Reuses the existing quoter views; nothing rebuilt. DRP + LRP quoters read only
// public RMA rate tables, so portal users get them too; admin-only signal tools
// (DRP Buy / LRP Push) are gated off in portal. ──
function hubTabStyle(active, isMobile) {
  return { padding: isMobile ? "10px 14px" : "12px 20px", fontSize: isMobile ? 13 : 14,
    fontWeight: active ? 700 : 500, color: active ? "#1a2e1a" : "#60735f",
    background: active ? "#fff" : "transparent", border: "none",
    borderBottom: active ? "3px solid #4a6e4a" : "3px solid transparent", cursor: "pointer", marginBottom: -1 };
}
function hubDefaultState(farmers) {
  const counts = {}; (farmers || []).forEach(f => { const s = (f.state || "").toUpperCase(); if (s) counts[s] = (counts[s] || 0) + 1; });
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || "NY";
}
function QuotersHub({ farmers = [], isMobile, mode = "admin", lobFilter = null, initialTab = null }) {
  const isPortal = mode === "portal";
  const defState = hubDefaultState(farmers);
  // DRP Buy Signal + LRP Push Signal are ADMIN-ONLY decision tools (not market
  // data, so they live here in the quote/advise hub, never in Markets or portal).
  const all = [
    { key: "drp",       label: "DRP Quoter",      icon: "🥛", lob: "drp" },
    ...(!isPortal ? [{ key: "drpsignal", label: "DRP Floor Quality",  icon: "🎯", lob: "drp" }] : []),
    // The LRP quoter reads only public RMA rate tables (no farmer PII), so both
    // admins and portal users get it. The Profit Calc is a separate tool.
    { key: "lrp",       label: "LRP Quoter",      icon: "🐄", lob: "lrp" },
    { key: "lrpprofit", label: "LRP Profit Calc", icon: "🧮", lob: "lrp" },
    ...(!isPortal ? [{ key: "lrpsignal", label: "LRP Push Signal", icon: "🎯", lob: "lrp" }] : []),
  ];
  const tabs = all.filter(t => !lobFilter || lobFilter.has(t.lob));
  const [activeTab, setActiveTab] = useState(() =>
    (initialTab && tabs.some(t => t.key === initialTab)) ? initialTab : (tabs[0]?.key || "drp"));
  const active = tabs.some(t => t.key === activeTab);
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        <h1 style={{ ...uiPageTitle(isMobile) }}>Quoters</h1>
        <div style={{ fontSize: 12.5, color: "#60735f", marginTop: 3 }}>Run a quote{!isPortal ? " and read the buy/push signals" : ""} for the coverages you carry.</div>
      </div>
      {tabs.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid #e4ece4", marginBottom: 20 }}>
          {tabs.map(t => <button key={t.key} onClick={() => setActiveTab(t.key)} style={hubTabStyle(activeTab === t.key, isMobile)}><span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}</button>)}
        </div>
      )}
      {!tabs.length && <div style={{ padding: 24, color: "#60735f", fontSize: 13 }}>No quoters available for your coverage.</div>}
      {active && activeTab === "drp" && <DRPRmaQuoter isMobile={isMobile} mode={mode} defaultState={defState} />}
      {active && activeTab === "drpsignal" && !isPortal && <DRPSignalView isMobile={isMobile} />}
      {active && activeTab === "lrp" && <LRPQuotingView isMobile={isMobile} />}
      {active && activeTab === "lrpprofit" && <LRPProfitView isMobile={isMobile} mode={mode} farmers={farmers} />}
      {active && activeTab === "lrpsignal" && !isPortal && <LRPCogSignalView isMobile={isMobile} sbFetch={sbFetch} />}
    </div>
  );
}
// ── MARKETS HUB ──────────────────────────────────────────────────────────────
// Every market tab the app has, in display order. `lob` is the coverage line
// that implies it when a user is following their coverage.
// ─────────────────────────────────────────────────────────────────────────────
// PORTAL PAGES — what a page allowlist may and may not reach.
//
// portal_users.page_access (sql/049) lets an admin say "this person sees these
// pages and nothing else". That is the feature; this registry is the guard on
// it, and the guard is the load-bearing half.
//
// THE CLASS DECIDES WHETHER AN ALLOWLIST MAY *GRANT* A PAGE OR ONLY *REMOVE* IT.
//
//   "open"   reads NO farmer data. An allowlist may grant it freely, because
//            there is nothing to scope. Verified per view, not assumed —
//            SilasHomeView is asserted farmer-free by tools/check-home-safe.js,
//            every MarketsHub tab by tools/check-market-tabs.js, and the MI
//            map by tools/check-mi-portal-farms.js.
//            ⚠ THAT LAST ONE USED TO READ "MarketIntelligenceView / AgCalcHub /
//            WeatherView take no farmer props at all", which is TRUE and weaker
//            than the claim it was supporting. Taking no farmer PROP is not
//            reading no farmer DATA: MIYieldMap fetched `farmers` itself and
//            rendered a "Customer farms" layer, in the portal, with RLS as the
//            only thing between a prospect and 207 growers' geocoded farms.
//            Verify what a view READS, never what it is handed.
//   "book"   is farmer-scoped. An allowlist may only REMOVE it. Ticking one for
//            a user with no farm grants must never put it on their nav: that
//            would leave RLS as the ONLY guard on a portal surface, which is
//            precisely the rule CLAUDE.md records two leaks behind.
//   "agency" is agency-internal — not farmer-scoped, not market data. Cash
//            Targets is the only one, and page_access can never reach it; it
//            needs its own deliberate flag (can_view_cash_targets), the same
//            way can_view_578 sits outside the ordinary checkbox row.
//
// This is the MARKET_TABS argument one level up. That array carries "nothing
// that reads farmer data belongs here" because an admin tick puts any entry in
// it in front of any user; page_access widens the same door, so the same
// question had to be answered for every page rather than for six market tabs.
// ─────────────────────────────────────────────────────────────────────────────
// Portal roles that are agency STAFF rather than customers. The File Management
// page is staff-only: "Farmers should be able to see their stored 578 within their
// account but they should not be able to see file management" (Kevin, 2026-11-02).
// A customer still gets the 578 Documents tab on their own coverage card, read-only
// and scoped by RLS (farmers_read_own_578s; storage by the farmer id in the path).
// Every other role — farmer, market, or a role left blank — is a customer, so an
// unknown role fails closed.
const PORTAL_STAFF_ROLES = new Set(["agent", "underwriter"]);
const PORTAL_PAGES = [
  // key              class     label (as the picker shows it)
  { key:"home",           cls:"book",   label:"Home (their farm)" },
  { key:"briefing",       cls:"open",   label:"Home (market briefing)" },
  { key:"coverage",       cls:"book",   label:"My Coverage" },
  { key:"quoters",        cls:"book",   label:"Quoters" },
  { key:"markets",        cls:"open",   label:"Markets" },
  { key:"prfreview",      cls:"book",   label:"PRF Coverage" },
  { key:"prfgrid",        cls:"book",   label:"PRF Grid" },
  { key:"claims",         cls:"book",   label:"Claims" },
  { key:"fields",         cls:"book",   label:"Field Mapper" },
  { key:"yieldanalytics", cls:"book",   label:"Yield Analytics" },
  { key:"fieldprogress",  cls:"book",   label:"Field Progress" },
  { key:"grain",          cls:"book",   label:"Grain Marketing" },
  { key:"cashtargets",    cls:"agency", label:"Cash Targets" },
  { key:"agcalc",         cls:"open",   label:"Ag Calculators" },
  { key:"rainwatch",      cls:"open",   label:"Weather" },
  { key:"alerts",         cls:"book",   label:"Price Alerts" },
  { key:"fsa578",         cls:"book",   label:"File Management" },
  { key:"market",         cls:"open",   label:"Market Intelligence" },
];
const PORTAL_PAGE_CLASS = PORTAL_PAGES.reduce((m, p) => { m[p.key] = p.cls; return m; }, {});

// THE one rule. Both the nav and the permission editor call it, so the editor
// can never promise a page the nav will not render — the same reason
// marketHubTabs is shared between MarketsHub and the nav.
//
// `baseNav` is what the ordinary gates already allowed. `pageSet` is the
// stored allowlist, and it is THREE-valued exactly like market_tabs:
//   null → follow the gates (what every user has today; adding the column
//          changed nobody's nav)
//   []   → an explicit empty allowlist: no pages
//   set  → exactly these, intersected with what the gates allow for a "book"
//          page, and ADDED for an "open" one.
// An "agency" page is only ever in `baseNav` when its own flag is set, so it
// passes through this filter and can never be conjured by it.
// Does this account reach ANYTHING at all? PortalGate asks it to decide whether
// to render the portal or the "No access yet" card; the Portal Access roster
// asks it to decide whether Preview can show anything. It was two hand-rolled
// conditions, and they had drifted — the roster still asked the question the
// portal asked before market-only accounts existed ("do they hold farms, the
// 578 flag, or the underwriter role?"), so an account that signs in perfectly
// well had no Preview button and no way to be checked.
//
// Measured on the live roster 2026-08-27: 45 active accounts, and exactly TWO
// could sign in but could not be previewed — jnevinger (the market/staff
// account this page allowlist was built for) and mmeisenzahl@cloverag.com
// (alerts only, no farms). Everyone else agreed, which is why nobody had
// noticed: the gate is correct for every account that holds a farm.
//
// `grantCount` is passed rather than read, because the two callers count
// active grants from different shapes (portal_users.grants vs the
// farmer_portal_users fetch).
// ⚠ A FARM GRANT IS NOT AN EFFECTIVE PAGE. This used to short-circuit on
// `grantCount > 0` BEFORE it looked at `page_access`, so an account holding a
// farm AND an explicitly empty page list reached the gate, signed in, and then
// rendered a nav with nothing in it — the page filter having removed every
// `book` page the grant was supposed to open.
//
// Reachability and effective pages are the SAME question and are answered in
// one place: an account reaches the portal exactly when at least one page
// survives its own allowlist. `[]` still means "reaches nothing" — it is a
// deliberate switch-everything-off, and a flag left on beside it must not
// rescue it — but an agency flag is not a page the picker can express, so it
// still counts on its own.
function portalAccountReaches(pu, grantCount) {
  if (!pu || pu.is_active === false) return false;
  if (Array.isArray(pu.page_access)) {
    // An explicit list decides, whatever the grants say. A `book` page in the
    // list only counts when a grant actually reaches it, which is the same rule
    // portalPagesFor applies — so the gate cannot disagree with the nav.
    const reachable = pu.page_access.filter((k) =>
      PORTAL_PAGE_CLASS[k] === "open" ||
      (PORTAL_PAGE_CLASS[k] === "book" && (grantCount || 0) > 0));
    return reachable.length > 0 || !!pu.can_view_cash_targets;
  }
  if ((grantCount || 0) > 0) return true;
  return !!(pu.can_view_mi || pu.can_set_alerts || pu.can_view_578 ||
            pu.can_view_model_farm || pu.can_view_cash_targets ||
            pu.role === "underwriter");
}

function portalPagesFor(baseNav, pageSet, openCatalog) {
  if (!pageSet) return baseNav;
  const have = new Set(baseNav.map(n => n.key));
  // An "open" page may be ADDED by the allowlist. Nothing else may.
  const added = (openCatalog || []).filter(n =>
    pageSet.has(n.key) && !have.has(n.key) && PORTAL_PAGE_CLASS[n.key] === "open");
  // ⚠ AN "agency" PAGE PASSES THROUGH UNTOUCHED — it is governed by its own
  // flag and by nothing else, in BOTH directions. The first cut filtered it
  // like everything else, which was a save that could not take: the picker
  // deliberately does not offer an agency page (it is not a page you grant by
  // ticking pages), so once an admin set ANY explicit list, Cash Targets could
  // never be in it and was stripped from the nav of the very staff member the
  // flag had just been switched on for. Found on live data 2026-08-27 —
  // jnevinger held can_view_cash_targets AND a five-page list, and the list won.
  //
  // The comment above this function claimed it "passes through this filter"
  // while the code filtered it, and check-staff-access agreed with the comment
  // because its fixture put "cashtargets" INSIDE the set. A test that names the
  // key it is checking for cannot see this; the case that matters is the key
  // ABSENT. Both are asserted now.
  return [...baseNav, ...added].filter(n =>
    PORTAL_PAGE_CLASS[n.key] === "agency" || pageSet.has(n.key));
}

const MARKET_TABS = [
  // Market DATA only, and as of 2026-08-21 that rule holds with NO exception:
  // Buy/Push signals are admin decision tools and live in the Quoters
  // (quote/advise) hub, and the PRF Grid — the one tab that read `farmers` and
  // drew the book over a map — moved to a page of its own. That is what makes
  // `marketLobSet` safe to widen past a customer's coverage. Nothing that reads
  // farmer data belongs in this array.
  { key: "grainmi",    label: "Grain MI",      icon: "🌾",  lob: "grain"  }, // spring OR fall
  { key: "milkmi",     label: "Milk MI",       icon: "🧠",  lob: "drp"    },
  { key: "milksupply", label: "Milk Supply",   icon: "📊",  lob: "drp"    },
  { key: "dmc",        label: "DMC",           icon: "🐄",  lob: "drp"    },
  { key: "cattlemkt",  label: "Cattle Market", icon: "📈",  lob: "lrp"    },
  { key: "cattlemi",   label: "Cattle MI",     icon: "🧠",  lob: "lrp"    },
];

// THE one rule for which market tabs a viewer gets. Both MarketsHub (what to
// render) and the portal nav (whether to offer Markets at all) call this, so a
// user can never be handed a Markets page with nothing on it, or be shown a tab
// the nav thinks they don't have.
//
// `tabFilter` is the PER-USER list from portal_users.market_tabs, and when it is
// present it is the WHOLE ANSWER — it both narrows and widens. NULL means follow
// coverage, which is what every user had before the column existed — so omitting
// the argument silently widens a restricted user back to their full coverage
// set. That is why it is in check-shared-rules' RULES: this is exactly the
// "rule called SHORT" defect that file exists to catch.
//
// ⚠ AN EXPLICIT LIST IS NOT A FILTER OVER THE COVERAGE SET — fixed 2026-08-21,
// and the two halves of this feature had disagreed since it shipped. This used
// to AND the two, so ticking Milk MI for a grower who carries no DRP stored the
// key and changed NOTHING on their page — while the editor beside it badged that
// same tick "+ Not implied by their coverage — turning this on shows it anyway".
// Measured on Stein Family Farms: market_tabs held all six, the grant held
// spring/prf/lrp, and the portal rendered Grain MI · Cattle Market · Cattle MI
// with Milk MI / Milk Supply / DMC silently gone. Nothing on either screen said
// the tick had been overruled, which is why it read as a save that didn't take.
//
// Widening is safe because EVERY view in MARKET_TABS reads NO farmer data —
// GrainMIView, MilkMIView, DRPPerformanceView, DMCView and CattleMIView take no
// farmer props at all, and LRPPerformanceView destructures `farmers` and never
// reads it (checked, not assumed). That is the same reasoning that already lets
// `marketLobSet` widen past a customer's coverage, and the one tab that DID read
// `farmers` — the PRF Grid — left this array on 2026-08-21 for its own page. So
// "nothing that reads farmer data belongs in MARKET_TABS" now has a SECOND
// reason behind it: an admin tick can put any entry here in front of any user,
// with no coverage standing between them.
function marketHubTabs(lobFilter, tabFilter) {
  if (tabFilter) return MARKET_TABS.filter(t => tabFilter.has(t.key));
  const has = (k) => !lobFilter || lobFilter.has(k);
  // Grain MI serves the row-crop growers — anyone carrying a spring or fall crop.
  const hasLob = (lob) => lob === "grain" ? (has("spring") || has("fall")) : has(lob);
  return MARKET_TABS.filter(t => hasLob(t.lob));
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPERIMENTAL MARKET BAROMETER v0 — INTERNAL, ADMIN ONLY (Kevin, 2026-10-19).
//
// Seven 0-100 leans computed by src/markets/barometerEngine.js behind
// /api/market?fn=barometer (checkAdmin). This component computes NOTHING: it
// draws the number the endpoint returns and the evidence behind it. It is NOT a
// MARKET_TABS entry and NOT a PORTAL_PAGES key — it is handed to MarketsHub as
// a node from the AGENT Dashboard branch only, behind isRealAdminSession(), and
// MarketsHub drops it in portal mode. tools/check-market-tabs.js and
// tools/check-staff-access.js assert both.
//
// EVERY GAUGE DRAWS IN FULL COLOUR (Kevin, 2026-10-20): "I'd rather have that than
// something grayed out that will never not be grayed out." The whole section is
// labelled EXPERIMENTAL instead; each gauge's live track record is still stored and
// scored daily and shown behind the click as "Track record", never on the face.
// Insufficient data draws no needle at all - that is missing data, not a grade.
// ─────────────────────────────────────────────────────────────────────────────
const BAROMETER_BANDS = [
  { lo: 0, hi: 20, color: "#b03a2e" },
  { lo: 20, hi: 40, color: "#e07b5f" },
  { lo: 40, hi: 60, color: "#e6c454" },
  { lo: 60, hi: 80, color: "#8cc46f" },
  { lo: 80, hi: 100, color: "#2f7d3a" },
];

function BarometerGauge({ g, selected, onOpen, isMobile }) {
  const W = 200, H = 116, cx = 100, cy = 100, r = 78;
  const pt = (v, rad) => { const th = Math.PI * (1 - v / 100); return [cx + rad * Math.cos(th), cy - rad * Math.sin(th)]; };
  const arc = (a, b) => { const [x1, y1] = pt(a, r), [x2, y2] = pt(b, r); return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`; };
  const hasNeedle = !g.insufficient && g.score != null;
  const needleColor = "#1a1a1a";
  const [nx, ny] = hasNeedle ? pt(Math.max(0, Math.min(100, g.score)), r - 14) : [cx, cy];
  return (
    <button type="button" onClick={onOpen} aria-pressed={selected}
      style={{ ...uiCard(12), cursor: "pointer", textAlign: "center", border: `1px solid ${selected ? T.inkSoft : T.line}`,
        boxShadow: selected ? "0 0 0 2px rgba(45,74,45,0.18)" : uiCard().boxShadow, fontFamily: "inherit", minWidth: 0 }}>
      <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 800, color: T.ink }}>{g.label}</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 220, display: "block", margin: "4px auto 0" }} role="img"
        aria-label={`${g.label}: ${hasNeedle ? g.score + " " + g.classification : "insufficient data"}`}>
        {BAROMETER_BANDS.map((b) => (
          <path key={b.lo} d={arc(b.lo + 0.6, b.hi - 0.6)} fill="none" stroke={b.color} strokeWidth="16" strokeLinecap="butt" />
        ))}
        {hasNeedle && <line x1={cx} y1={cy} x2={nx.toFixed(2)} y2={ny.toFixed(2)} stroke={needleColor} strokeWidth="3.2" strokeLinecap="round" />}
        <circle cx={cx} cy={cy} r="6" fill={hasNeedle ? needleColor : "#c8c8c8"} />
      </svg>
      <div style={{ fontSize: 26, fontWeight: 800, color: T.ink, lineHeight: 1.1, ...uiNum }}>{hasNeedle ? Math.round(g.score) : "—"}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.inkSoft, minHeight: 16 }}>{hasNeedle ? g.classification : "Insufficient data"}</div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 6, lineHeight: 1.35, fontWeight: 700 }}>{g.plain ? g.plain.question.window : g.target}</div>
    </button>
  );
}

function BarometerDetail({ g, isMobile }) {
  const th = { textAlign: "left", fontSize: 10.5, fontWeight: 800, color: T.faint, textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 8px", borderBottom: `1px solid ${T.lineSoft}` };
  const td = { fontSize: 12, color: T.ink, padding: "6px 8px", borderBottom: `1px solid ${T.lineSoft}`, verticalAlign: "top" };
  const pts = (v) => (v == null ? "—" : (v > 0 ? "+" : "") + v);
  const ev = g.evidence || {};
  const primary = (g.scorecard || []).find((c) => c.horizon === g.primaryHorizon && c.modelVersion === g.modelVersion);
  const ev1 = (list, title) => (
    <div style={{ flex: "1 1 220px", minWidth: 0 }}>
      <div style={{ ...uiLabel, marginBottom: 4 }}>{title}</div>
      {list && list.length ? list.map((c) => (
        <div key={c.key} style={{ fontSize: 12.5, color: T.ink, marginBottom: 3 }}>
          <b>{c.label}</b> <span style={{ color: T.muted, ...uiNum }}>{pts(c.points)} pts</span>
          {c.observation && <span style={{ color: T.muted }}> · {c.observation.value} {c.observation.unit}</span>}
        </div>
      )) : <div style={{ fontSize: 12, color: T.muted }}>None</div>}
    </div>
  );
  return (
    <div style={{ ...uiCard(isMobile ? 12 : 16), marginTop: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline", justifyContent: "space-between" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>{g.label} — {g.insufficient ? "Insufficient data" : `${Math.round(g.score)} · ${g.classification}`}</div>
        {g.plain && <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 20, background: T.panel || "#f2f6f2", color: T.inkSoft, border: `1px solid ${T.line}` }}>{g.plain.question.window}</span>}
      </div>
      {g.plain && (
        <div style={{ marginTop: 8, fontSize: 13.5, color: T.ink, lineHeight: 1.5 }}>
          <div><b>What it's answering:</b> {g.plain.question.text}</div>
          <div style={{ marginTop: 6 }}><b>Why it reads this way:</b> {g.plain.why}</div>
          <div style={{ marginTop: 6, color: T.body }}><b>How much to trust it:</b> {g.plain.trackRecord}</div>
        </div>
      )}
      <details style={{ marginTop: 12 }}>
      <summary style={{ fontSize: 12.5, color: T.inkSoft, cursor: "pointer", fontWeight: 700 }}>Show the details</summary>
      <div style={{ fontSize: 11.5, color: T.muted, marginTop: 6 }}>as of {g.asOf} · {g.modelVersion} · weights {g.weightsVersion}</div>
      <div style={{ fontSize: 12.5, color: T.body, marginTop: 4 }}><b>Target:</b> {g.target}
        {g.targetObservation && <span> — last {g.targetObservation.value} ({g.targetObservation.on}, {g.targetObservation.asOf})</span>}</div>
      <div style={{ fontSize: 12.5, color: T.body, marginTop: 4 }}><b>Track record: {g.statusLabel}.</b> {g.statusBasis}</div>
      {g.scale && g.rawScore != null && (g.scale.basis === "own_history"
        ? <div style={{ fontSize: 12.5, color: T.body, marginTop: 4 }}><b>Scale:</b> the inputs net to a raw lean of {g.rawScore}; this market's inputs typically lean {g.scale.typicalLean} points from 50 ({g.scale.n} past readings since {g.scale.since}), so today is {g.scale.z}x a typical lean and the needle reads {g.score}.</div>
        : <div style={{ fontSize: 12.5, color: T.body, marginTop: 4 }}><b>Scale:</b> raw lean {g.rawScore}, unstretched — only {g.scale.n} past readings, fewer than the {g.scale.need} needed to measure this market's typical lean.</div>)}
      {ev.note && <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{ev.note}</div>}
      {(g.summary || []).map((l, i) => <div key={i} style={{ fontSize: 12.5, color: T.ink, marginTop: i ? 2 : 8 }}>{l}</div>)}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12 }}>
        {ev1(g.strongestBullish, "Strongest bullish evidence")}
        {ev1(g.strongestBearish, "Strongest bearish evidence")}
      </div>

      <div style={{ ...uiLabel, marginTop: 14, marginBottom: 4 }}>Indicator breakdown</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
          <thead><tr>{["Indicator", "Direction", "Points", "Latest observation", "Source", "As of"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {(g.contributions || []).map((c) => (
              <tr key={c.key}>
                <td style={td}><b>{c.label}</b><div style={{ fontSize: 10.5, color: T.muted }}>{c.definition} · sign {c.sign > 0 ? "+" : "−"}</div></td>
                <td style={td}>{c.signed > 0 ? "Bullish" : c.signed < 0 ? "Bearish" : "Flat"}</td>
                <td style={{ ...td, ...uiNum }}>{pts(c.points)}</td>
                <td style={td}>{c.observation ? `${c.observation.value} ${c.observation.unit}` : "—"}</td>
                <td style={{ ...td, color: T.muted }}>{c.source}</td>
                <td style={{ ...td, color: T.muted, ...uiNum }}>{c.asOf || "—"}</td>
              </tr>
            ))}
            {(g.excluded || []).map((c) => (
              <tr key={"x" + c.key}>
                <td style={{ ...td, color: T.muted }}>{c.label}</td>
                <td style={{ ...td, color: T.muted }} colSpan={5}>Not counted — {c.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Points = each input's share of the move away from 50 (equal weights). Inputs are ranked against their own past only.</div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0,1fr))", gap: 12, marginTop: 14 }}>
        <div>
          <div style={uiLabel}>Data completeness</div>
          <div style={{ fontSize: 12.5, color: T.ink, marginTop: 3 }}>{g.completeness.present} of {g.completeness.expected} inputs current</div>
          {g.completeness.stale.length > 0 && <div style={{ fontSize: 11.5, color: T.warn }}>Stale: {g.completeness.stale.map((s) => s.label).join(", ")}</div>}
          {g.completeness.absent.length > 0 && <div style={{ fontSize: 11.5, color: T.muted }}>Missing: {g.completeness.absent.map((s) => s.label).join(", ")}</div>}
        </div>
        <div>
          <div style={uiLabel}>Indicator disagreement</div>
          <div style={{ fontSize: 12.5, color: T.ink, marginTop: 3 }}>{g.disagreement.bullish} bullish · {g.disagreement.bearish} bearish{g.disagreement.flat ? ` · ${g.disagreement.flat} flat` : ""}</div>
          {g.disagreement.sd != null && <div style={{ fontSize: 11.5, color: T.muted }}>spread (SD of signed scores) {g.disagreement.sd}</div>}
        </div>
        <div>
          <div style={uiLabel}>Confidence (measured record)</div>
          <div style={{ fontSize: 12, color: T.ink, marginTop: 3 }}>
            {ev.holdoutIC != null ? <>Backtest holdout: IC {ev.holdoutIC}, n_eff {ev.nEff}{ev.bh ? ", survived multiple-testing" : ", not significant"}</> : "Not tested in the backtest"}
          </div>
          <div style={{ fontSize: 12, color: T.ink }}>
            {primary && primary.ic != null ? <>Live: IC {primary.ic}, n_eff {primary.nEff}, {primary.months} months{primary.bestBenchmark ? ` · best benchmark ${primary.bestBenchmark.name} ${primary.bestBenchmark.ic}` : ""}{primary.hitBull ? ` · bullish hit ${Math.round(primary.hitBull.hit * 100)}% (n ${primary.hitBull.n})` : ""}</>
              : <>Live record: none scored yet (VOID){primary ? ` — ${primary.n} outcome${primary.n === 1 ? "" : "s"}` : ""}</>}
          </div>
        </div>
      </div>

      <div style={{ ...uiLabel, marginTop: 14 }}>What changed since {g.changed ? g.changed.prevAsOf : "yesterday"}</div>
      {!g.changed ? <div style={{ fontSize: 12, color: T.muted }}>No earlier read to compare.</div> : (
        <div style={{ fontSize: 12.5, color: T.ink, marginTop: 3 }}>
          {g.changed.delta == null ? `Score ${g.changed.scoreFrom ?? "—"} → ${g.changed.scoreTo ?? "—"}` : `Score ${g.changed.scoreFrom} → ${g.changed.scoreTo} (${g.changed.delta > 0 ? "+" : ""}${g.changed.delta})`}
          {g.changed.moves.length === 0 ? " · no input moved" : g.changed.moves.map((m) => (
            <div key={m.key} style={{ fontSize: 12, color: T.muted }}>{m.label}: {pts(m.from)} → {pts(m.to)} pts</div>))}
        </div>
      )}

      <details style={{ marginTop: 12 }}>
        <summary style={{ fontSize: 12, color: T.inkSoft, cursor: "pointer", fontWeight: 700 }}>Benchmarks and challenger (recorded, not on the needle)</summary>
        <div style={{ fontSize: 12, color: T.body, marginTop: 6 }}>
          {g.benchmarks && <div>Momentum {g.benchmarks.momentum ?? "—"} · Seasonality {g.benchmarks.seasonality ?? "—"}{g.kind === "grain" ? ` · Carry ${g.benchmarks.carry ?? "—"}` : ""} · Always-50</div>}
          <div>Ridge challenger: {g.challenger && g.challenger.ridge ? `${g.challenger.ridge.score} (fit on ${g.challenger.ridge.trainedOn} rows realised before ${g.challenger.ridge.fitThrough})` : "not enough history"}</div>
          {(g.scorecard || []).length > 0 && <div style={{ marginTop: 4 }}>Live scorecard by horizon: {(g.scorecard || []).map((c) => `${c.horizon}: IC ${c.ic ?? "—"} (n_eff ${c.nEff})`).join(" · ")}</div>}
        </div>
      </details>
      </details>
    </div>
  );
}

function MarketBarometerSection({ isMobile }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const res = await adminApiFetch("/api/market?fn=barometer");
        const body = await apiJson(res, "The barometer");
        if (!dead) setData(body);
      } catch (e) { if (!dead) setErr(e.message); }
    })();
    return () => { dead = true; };
  }, []);
  const sel = data && open ? data.gauges.find((g) => g.key === open) : null;
  return (
    <section style={{ marginBottom: 22 }} aria-label="Experimental market barometer">
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.ink }}>Market Barometer</div>
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", padding: "2px 8px", borderRadius: 20, background: "#fff4d6", color: "#7a5a00", border: "1px solid #ecd58f" }}>EXPERIMENTAL · ADMIN ONLY</span>
        {data && <span style={{ fontSize: 11.5, color: T.muted }}>as of {data.asOf}</span>}
      </div>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 10, lineHeight: 1.45 }}>
        A 0–100 lean per market — 50 means the inputs balance, above is bullish, below is bearish — from an equal-weight composite of the inputs listed under each gauge, stretched by how far that market's inputs typically lean so a strong read reaches the outer bands.
        Every gauge is an experimental read, not a proven forecast; click one for its evidence and its live track record.
        <details style={{ display: "inline" }}><summary style={{ display: "inline", cursor: "pointer", color: T.inkSoft, fontWeight: 700, marginLeft: 4 }}>how this works</summary>
          <div style={{ marginTop: 6 }}>Each input is ranked against its own past (never the future), signed by a rule written before the test, and averaged with equal weights: 50 + 50 × the mean.
          Stale inputs are left out and named; with fewer than half the inputs current a gauge reads Insufficient data. Every day's read is stored and scored against what happened at 10/30/60/90 days (or the next three months);
          that record is shown behind each gauge as its track record. The number is never set by AI.</div></details>
      </div>
      {err && <div style={{ ...uiCard(12), color: T.danger, fontSize: 12.5 }}>{err}</div>}
      {!data && !err && <div style={{ fontSize: 12.5, color: T.muted }}><UiSpinner />Loading the barometer…</div>}
      {data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 150 : 190}px, 1fr))`, gap: isMobile ? 8 : 12 }}>
            {data.gauges.map((g) => (
              <BarometerGauge key={g.key} g={g} isMobile={isMobile} selected={open === g.key} onOpen={() => setOpen(open === g.key ? null : g.key)} />
            ))}
          </div>
          {sel && <BarometerDetail g={sel} isMobile={isMobile} />}
          {(data.notes || []).length > 0 && <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>{data.notes.join(" · ")}</div>}
          {!data.record.tables && <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>No live record yet — the daily record tables are not created.</div>}
        </>
      )}
    </section>
  );
}

// Reuses every existing market view. Grain MI is added in its own phase.
// `barometer` is a node handed in by the AGENT Dashboard only (admin-only
// Market Barometer); the portal never passes one, and isPortal drops it anyway.
function MarketsHub({ farmers = [], isMobile, mode = "admin", lobFilter = null, initialLob = null, tabFilter = null, barometer = null }) {
  const isPortal = mode === "portal";
  const defState = hubDefaultState(farmers);
  const tabs = marketHubTabs(lobFilter, tabFilter);
  // A coverage→market jump focuses that LOB's first market tab.
  const lobFirstTab = { spring: "grainmi", fall: "grainmi", drp: "milkmi", lrp: "cattlemkt" };
  const [activeTab, setActiveTab] = useState(() => {
    const want = initialLob && lobFirstTab[initialLob];
    return (want && tabs.some(t => t.key === want)) ? want : (tabs[0]?.key || "milkmi");
  });
  // GATE AT THE RENDER, not just at the tab button. Without this, a viewer whose
  // tab list is empty still fell through to the `|| "milkmi"` default above and
  // MilkMIView mounted for someone entitled to no market pages at all. The same
  // guard QuotersHub already carries; per-user market selection is what made this
  // path reachable.
  const active = tabs.some(t => t.key === activeTab);
  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        <h1 style={{ ...uiPageTitle(isMobile) }}>Markets</h1>
        <div style={{ fontSize: 12.5, color: "#60735f", marginTop: 3 }}>Market data & intelligence for the markets you follow.</div>
      </div>
      {!isPortal && barometer}
      {tabs.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid #e4ece4", marginBottom: 20 }}>
          {tabs.map(t => <button key={t.key} onClick={() => setActiveTab(t.key)} style={hubTabStyle(activeTab === t.key, isMobile)}><span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}</button>)}
        </div>
      )}
      {!tabs.length && <div style={{ padding: 24, color: "#60735f", fontSize: 13 }}>No market pages are turned on for your account. Contact your agent if you expected one here.</div>}
      {active && activeTab === "grainmi" && <GrainMIView isMobile={isMobile} />}
      {active && activeTab === "milkmi" && <MilkMIView isMobile={isMobile} mode={mode} defaultState={defState} />}
      {active && activeTab === "milksupply" && <DRPPerformanceView isMobile={isMobile} defaultState={defState} />}
      {active && activeTab === "dmc" && <DMCView isMobile={isMobile} />}
      {active && activeTab === "cattlemkt" && <LRPPerformanceView farmers={farmers} isMobile={isMobile} mode={mode} />}
      {active && activeTab === "cattlemi" && <CattleMIView isMobile={isMobile} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AG CALCULATORS — the agronomy/field tools that used to live on cloverag.com.
//
// Moved in-app 2026-08-03 so cloverag.com can go back to being an information
// site and every working tool lives behind one login. These four are the ones
// with NO existing SILAS equivalent. The other two website calculators were
// merged into the engines that already do that job rather than ported:
//   Grain Cost of Production → the COP engine in GrainMarketingView
//                              (COP_CATEGORIES / COP_DEFAULTS), which gained
//                              per-pass Field Operations in the same change.
//   Basic Cattle Breakeven   → LRPProfitView, which gained multi-lot blending.
//
// Everything here is pure input→output math on numbers the user types. No
// farmer PII is read, so the whole hub is portal-safe and `mode` only affects
// copy, never gating.
// ─────────────────────────────────────────────────────────────────────────────

// Shared control styling — matches the LRP/DRP quoter forms so the hub doesn't
// read as a bolted-on third-party widget.
const AC_LBL  = { fontSize: 11, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" };
const AC_INP  = { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d0e0d0", fontSize: 14, fontVariantNumeric: "tabular-nums", textAlign: "right", boxSizing: "border-box" };
const AC_CTRL = { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d0e0d0", fontSize: 14, background: "#fff", boxSizing: "border-box", fontFamily: "inherit" };
const AC_SUFFIX = { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#677167", pointerEvents: "none" };

// Parse a form value. Blank/garbage reads 0 rather than NaN so a half-filled
// form still renders numbers instead of "—" everywhere.
function acNum(v) { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; }
// Same, but blank stays null so "not entered" can be told from "entered zero".
function acNumOrNull(v) { if (v === "" || v == null) return null; const n = parseFloat(v); return Number.isFinite(n) ? n : null; }

function AcCard({ title, sub, children, style }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: "14px 16px", marginBottom: 16, ...style }}>
      {title && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#2d4a2d", textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</div>
          {sub && <div style={{ fontSize: 11.5, color: "#60735f", marginTop: 3 }}>{sub}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function AcField({ label, value, onChange, suffix, step = "0.01", hint, type = "number", min }) {
  return (
    <div>
      <span style={AC_LBL}>{label}</span>
      <div style={{ position: "relative" }}>
        <input type={type} step={step} min={min} value={value} onChange={e => onChange(e.target.value)}
          style={{ ...AC_INP, paddingRight: suffix ? 14 + String(suffix).length * 7.5 : 10 }} />
        {suffix && <span style={AC_SUFFIX}>{suffix}</span>}
      </div>
      {hint && <div style={{ fontSize: 10, color: "#677167", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

// Headline number. `tone` picks the accent: good / warn / bad / neutral.
function AcStat({ k, v, sub, tone = "neutral", big = false }) {
  const tones = {
    good:    { bg: "#eef7ee", br: "#bcdcbc", fg: "#1f6f3a" },
    warn:    { bg: "#fdf7e8", br: "#e6d5a8", fg: "#8a6d1f" },
    bad:     { bg: "#fdf0ea", br: "#f0cbb0", fg: "#a3271f" },
    neutral: { bg: "#f7faf7", br: "#dde7dd", fg: "#1a2e1a" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.br}`, borderRadius: 9, padding: "11px 13px" }}>
      <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#60735f" }}>{k}</div>
      <div style={{ fontSize: big ? 26 : 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: t.fg, marginTop: 2, lineHeight: 1.15 }}>{v}</div>
      {sub && <div style={{ fontSize: 10.5, color: "#677167", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function AcNote({ children, tone = "neutral" }) {
  const c = tone === "warn" ? { bg: "#fdf7e8", br: "#e6d5a8", fg: "#7a5f18" }
    : tone === "bad" ? { bg: "#fdf0ea", br: "#f0cbb0", fg: "#8f3520" }
    : { bg: "#f2f6f2", br: "#dde7dd", fg: "#4a6e4a" };
  return <div style={{ background: c.bg, border: `1px solid ${c.br}`, borderLeft: `3px solid ${c.br}`, color: c.fg, borderRadius: 7, padding: "9px 12px", fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>{children}</div>;
}

function acGrid(isMobile, cols) { return { display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : `repeat(${cols}, 1fr)`, gap: 12 }; }

// ── HIGH-MOISTURE CORN ───────────────────────────────────────────────────────
// Converts between a high-moisture corn price ($/ton, as-fed) and its dry-corn
// equivalent ($/bu at 15.5%), on a DRY-MATTER basis:
//
//   1 ton of m% corn carries 2000 × (1 − m/100) lb of dry matter.
//   At 15.5% moisture that same dry matter weighs DM ÷ 0.845 lb,
//   which is (DM ÷ 0.845) ÷ 56 bushels.
//
// So "35.71 bu/ton" (2000 ÷ 56) is only true AT 15.5% — at 22.5% a ton is worth
// 32.76 dry-equivalent bushels, and quoting 35.71 overstates the load by 9%.
//
// Both directions are offered because the negotiation runs both ways: the buyer
// asks "what's this wet corn worth per ton", the grower asks "the elevator pays
// $X dry, what do I need per ton to match".
const HM_TARGET_MOISTURE = 15.5;
const HM_LB_PER_BU = 56;

// Dry-equivalent bushels contained in one ton of corn at `m` percent moisture.
function hmBuPerTon(m) {
  const dmLb = 2000 * (1 - m / 100);
  return dmLb / (1 - HM_TARGET_MOISTURE / 100) / HM_LB_PER_BU;
}

function HMCornCalc({ isMobile }) {
  const [dir, setDir]             = useState("hm2dry"); // hm2dry | dry2hm
  const [moisture, setMoisture]   = useState("22.5");
  const [hmTon, setHmTon]         = useState("150");
  const [dryBu, setDryBu]         = useState("4.30");
  const [dryMode, setDryMode]     = useState("point");  // point | flat
  const [dryPerPoint, setDryPerPoint] = useState("0.05");
  const [dryFlat, setDryFlat]     = useState("0.35");
  const [otherCost, setOtherCost] = useState("0.03");

  const m = acNum(moisture);
  const valid = m > HM_TARGET_MOISTURE && m < 45;
  const buPerTon = valid ? hmBuPerTon(m) : 0;
  const points = valid ? m - HM_TARGET_MOISTURE : 0;
  // Drying + shrink is the cost the grower AVOIDS by selling wet, so it is a
  // credit to the wet corn. Handling/hauling is a cost that offsets it.
  const dryCredit = dryMode === "point" ? acNum(dryPerPoint) * points : acNum(dryFlat);
  const other = acNum(otherCost);
  const netAdj = dryCredit - other;

  const dryEquiv = valid && buPerTon > 0 ? acNum(hmTon) / buPerTon + netAdj : 0;
  const hmValue  = valid ? (acNum(dryBu) - netAdj) * buPerTon : 0;
  // Shrink: pounds lost taking a ton from m% down to 15.5%.
  const shrinkPct = valid ? (1 - (buPerTon * HM_LB_PER_BU) / 2000) * 100 : 0;
  const dmPerTon = valid ? 2000 * (1 - m / 100) : 0;
  // $/ton of DRY MATTER — the only figure that compares a wet corn bid against
  // silage or haylage without the water skewing it.
  const dmTon = valid && dmPerTon > 0
    ? (dir === "hm2dry" ? acNum(hmTon) : hmValue) / (dmPerTon / 2000)
    : 0;

  const rows = [];
  for (let mm = 18; mm <= 34; mm += 2) {
    const bpt = hmBuPerTon(mm);
    const pts = mm - HM_TARGET_MOISTURE;
    const adj = (dryMode === "point" ? acNum(dryPerPoint) * pts : acNum(dryFlat)) - other;
    rows.push({ m: mm, bpt, dry: acNum(hmTon) / bpt + adj, ton: (acNum(dryBu) - adj) * bpt });
  }

  const th = { textAlign: "right", padding: "6px 8px", fontSize: 10, fontWeight: 800, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e4ece4" };
  // The input a column was computed from, printed under its heading. See the
  // note on the moisture table below: one of those two columns always runs off
  // an input the direction toggle is currently hiding.
  const thSub = { fontSize: 10, fontWeight: 600, color: "#a3b3a3", textTransform: "none", letterSpacing: 0, marginTop: 1 };
  const td = { textAlign: "right", padding: "6px 8px", fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: "#1a2e1a", borderBottom: "1px solid #f2f6f2" };

  return (
    <div>
      <AcNote>
        A ton of corn is only <strong>35.7 bushels at 15.5% moisture</strong>. At {valid ? m.toFixed(1) : "—"}% it carries{" "}
        <strong>{valid ? buPerTon.toFixed(2) : "—"} dry-equivalent bushels</strong> — quoting 35.7 on wet corn overstates the load by{" "}
        {valid ? (35.714 / buPerTon * 100 - 100).toFixed(1) : "—"}%.
      </AcNote>

      <AcCard title="Which way are you converting?">
        <div style={{ display: "flex", gap: 4, background: "#f0f4f0", borderRadius: 8, padding: 3, marginBottom: 14, flexWrap: "wrap" }}>
          {[{ k: "hm2dry", l: "Wet $/ton → dry $/bu" }, { k: "dry2hm", l: "Dry $/bu → wet $/ton" }].map(o => (
            <button key={o.k} onClick={() => setDir(o.k)}
              style={{ flex: "1 1 auto", padding: "7px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700, fontFamily: "inherit",
                background: dir === o.k ? "#fff" : "transparent", color: dir === o.k ? "#1a2e1a" : "#60735f",
                boxShadow: dir === o.k ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}>{o.l}</button>
          ))}
        </div>
        <div style={acGrid(isMobile, 3)}>
          <AcField label="Moisture" value={moisture} onChange={setMoisture} suffix="%" step="0.1" hint="Typical HMC 22–30%" />
          {dir === "hm2dry"
            ? <AcField label="Wet corn price" value={hmTon} onChange={setHmTon} suffix="$/ton" step="0.50" hint="What you're being offered" />
            : <AcField label="Dry corn bid" value={dryBu} onChange={setDryBu} suffix="$/bu" step="0.01" hint="Elevator bid at 15.5%" />}
          <AcField label="Handling / haul" value={otherCost} onChange={setOtherCost} suffix="$/bu" hint="Extra cost of the wet sale" />
        </div>
      </AcCard>

      <AcCard title="Drying cost you avoid" sub="Selling wet means you never pay to dry it — that saving belongs to the wet price.">
        <div style={{ display: "flex", gap: 4, background: "#f0f4f0", borderRadius: 8, padding: 3, marginBottom: 12, maxWidth: 420 }}>
          {[{ k: "point", l: "Per point" }, { k: "flat", l: "Flat $/bu" }].map(o => (
            <button key={o.k} onClick={() => setDryMode(o.k)}
              style={{ flex: 1, padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                background: dryMode === o.k ? "#fff" : "transparent", color: dryMode === o.k ? "#1a2e1a" : "#60735f",
                boxShadow: dryMode === o.k ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}>{o.l}</button>
          ))}
        </div>
        <div style={acGrid(isMobile, 3)}>
          {dryMode === "point"
            ? <AcField label="Drying charge" value={dryPerPoint} onChange={setDryPerPoint} suffix="$/pt/bu" hint={valid ? `${points.toFixed(1)} points × ${fmtD(acNum(dryPerPoint))} = ${fmtD(dryCredit)}/bu` : "Enter a moisture above 15.5%"} />
            : <AcField label="Drying charge" value={dryFlat} onChange={setDryFlat} suffix="$/bu" hint="All-in, regardless of points" />}
        </div>
      </AcCard>

      {!valid ? (
        <AcNote tone="warn">Enter a moisture between 15.6% and 45% to convert.</AcNote>
      ) : (
        <>
          <div style={{ ...acGrid(isMobile, 4), marginBottom: 16 }}>
            {dir === "hm2dry"
              ? <AcStat k="Dry corn equivalent" v={`${fmtD(dryEquiv)}/bu`} sub="at 15.5% moisture" tone="good" big />
              : <AcStat k="Wet corn is worth" v={`${fmtD(hmValue)}/ton`} sub={`as-fed at ${m.toFixed(1)}%`} tone="good" big />}
            <AcStat k="Dry-equiv bushels" v={buPerTon.toFixed(2)} sub="per wet ton" />
            <AcStat k="Shrink to 15.5%" v={`${shrinkPct.toFixed(1)}%`} sub={`${fmt(2000 - buPerTon * HM_LB_PER_BU, 0)} lb water per ton`} />
            <AcStat k="Dry-matter price" v={`${fmtD0(dmTon)}/ton DM`} sub={`${fmt(dmPerTon, 0)} lb DM per wet ton`} />
          </div>

          <AcCard title="How it lands">
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {[
                dir === "hm2dry"
                  ? [`${fmtD(acNum(hmTon))}/ton ÷ ${buPerTon.toFixed(2)} bu`, `${fmtD(acNum(hmTon) / buPerTon)}/bu`]
                  : [`${fmtD(acNum(dryBu))}/bu dry bid`, `${fmtD(acNum(dryBu))}/bu`],
                [`+ drying you don't pay`, `+${fmtD(dryCredit)}/bu`],
                [`− handling / haul`, `−${fmtD(other)}/bu`],
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#4a6e4a", padding: "5px 0", borderBottom: "1px solid #f2f6f2" }}>
                  <span>{r[0]}</span><span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#1a2e1a" }}>{r[1]}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, fontWeight: 800, color: "#1f6f3a", paddingTop: 4 }}>
                <span>{dir === "hm2dry" ? "Dry corn equivalent" : "Wet corn value"}</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>{dir === "hm2dry" ? `${fmtD(dryEquiv)}/bu` : `${fmtD(hmValue)}/ton`}</span>
              </div>
            </div>
          </AcCard>

          <AcCard title="Across the moisture range" sub="Same drying and handling assumptions, moisture varied.">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 400 }}>
                <thead><tr>
                  {/* ⚠ ONE OF THESE TWO COLUMNS IS COMPUTED FROM AN INPUT THAT IS NOT
                      ON SCREEN. The direction toggle renders EITHER the wet price OR
                      the dry bid, and this table prints both conversions — so in
                      "Wet → dry" the Wet-value column runs off `dryBu`, whose default
                      is 4.30 and which the reader has never seen. Live on the default
                      view: the grower types $150/ton and the row two inches below says
                      their 22% corn is worth $132/ton. Two figures for one thing on one
                      card, 12% apart, the second from a hidden constant.

                      Both columns stay — an agent does want the round trip — but the
                      header now NAMES the figure the column was computed from, so
                      nothing on this card comes from a number the reader cannot see. */}
                  <th style={{ ...th, textAlign: "left" }}>Moisture</th>
                  <th style={th}>Dry-equiv bu/ton</th>
                  <th style={th}>Dry equivalent<div style={thSub}>{dir === "hm2dry" ? "from your " + fmtD0(acNum(hmTon)) + "/ton" : "at " + fmtD0(acNum(hmTon)) + "/ton"}</div></th>
                  <th style={th}>Wet value<div style={thSub}>{dir === "dry2hm" ? "from your " + fmtD(acNum(dryBu)) + "/bu" : "at " + fmtD(acNum(dryBu)) + "/bu"}</div></th>
                </tr></thead>
                <tbody>
                  {rows.map(r => {
                    const here = Math.abs(r.m - m) < 1;
                    return (
                      <tr key={r.m} style={here ? { background: "#eef7ee" } : undefined}>
                        <td style={{ ...td, textAlign: "left", fontWeight: here ? 800 : 400 }}>{r.m.toFixed(1)}%</td>
                        <td style={td}>{r.bpt.toFixed(2)}</td>
                        <td style={td}>{fmtD(r.dry)}/bu</td>
                        <td style={td}>{fmtD0(r.ton)}/ton</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </AcCard>
        </>
      )}
    </div>
  );
}

// ── YIELD CONTEST ────────────────────────────────────────────────────────────
// NYC&SGA official contest formulas, unchanged — the divisors are fixed by the
// contest, not by us: corn 47.32 (56 lb × 0.845 at 15.5%), soybeans 52.2
// (60 lb × 0.87 at 13%). Every intermediate is rounded to 2 decimals because
// the contest form does, and rounding only at the end gives a different answer
// than the judges will get.
//
// Added over the website version: corn area can be entered as rows × length
// (which is what a grower actually measures) instead of only a pre-summed total
// row length, and the eligibility rules are checked live rather than printed as
// prose underneath.
const YC_CORN_DIVISOR = 47.32;
const YC_SOY_DIVISOR  = 52.20;
const YC_SQFT_PER_ACRE = 43560;
const r2 = (n) => Math.round(n * 100) / 100;

function YieldContestCalc({ isMobile }) {
  const [crop, setCrop] = useState("corn");

  // Corn
  const [areaMode, setAreaMode] = useState("rows"); // rows | total
  const [rowSpacing, setRowSpacing] = useState("30");
  const [rowCount, setRowCount]     = useState("22");
  const [rowLength, setRowLength]   = useState("1100");
  const [totalRowLen, setTotalRowLen] = useState("24200");
  const [cornLbs, setCornLbs]       = useState("16580");
  const [cornMoist, setCornMoist]   = useState("21.25");
  const [second, setSecond]         = useState("");

  // Soybeans
  const [soyLen, setSoyLen]     = useState("180");
  const [soyWid, setSoyWid]     = useState("585");
  const [soyLbs, setSoyLbs]     = useState("7200");
  const [soyMoist, setSoyMoist] = useState("15.5");

  // Corn chain — every step rounded to 2dp, exactly as the contest form runs it.
  const rowWidthFt   = r2(acNum(rowSpacing) / 12);
  const effTotalRow  = areaMode === "rows" ? acNum(rowCount) * acNum(rowLength) : acNum(totalRowLen);
  const cornAcres    = r2(effTotalRow * rowWidthFt / YC_SQFT_PER_ACRE);
  const cornMoistAdj = (100 - acNum(cornMoist)) * 0.01;
  const cornBu       = r2(acNum(cornLbs) * cornMoistAdj / YC_CORN_DIVISOR);
  const cornYield    = cornAcres > 0 ? r2(cornBu / cornAcres) : 0;
  const secondVal    = acNumOrNull(second);
  const cornFinal    = secondVal != null ? r2((cornYield + secondVal) / 2) : cornYield;
  const needsSecond  = cornYield > 350;

  // Soybean chain
  const soySqFt   = acNum(soyLen) * acNum(soyWid);
  const soyAcres  = r2(soySqFt / YC_SQFT_PER_ACRE);
  const soyAdj    = (100 - acNum(soyMoist)) * 0.01;
  const soyYield  = soyAcres > 0 ? r2(acNum(soyLbs) * soyAdj / YC_SOY_DIVISOR / soyAcres) : 0;

  const step = (n, title, body, formula) => (
    <AcCard title={`Step ${n} — ${title}`}>
      {body}
      <div style={{ marginTop: 11, background: "#f7faf7", border: "1px solid #e4ece4", borderRadius: 7, padding: "8px 11px", fontSize: 11.5, color: "#5a7a5a", fontVariantNumeric: "tabular-nums" }}>{formula}</div>
    </AcCard>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 4, background: "#f0f4f0", borderRadius: 8, padding: 3, marginBottom: 16, maxWidth: 420 }}>
        {[{ k: "corn", l: "🌽 Corn" }, { k: "soybean", l: "🌱 Soybeans" }].map(o => (
          <button key={o.k} onClick={() => setCrop(o.k)}
            style={{ flex: 1, padding: "8px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
              background: crop === o.k ? "#fff" : "transparent", color: crop === o.k ? "#1a2e1a" : "#60735f",
              boxShadow: crop === o.k ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}>{o.l}</button>
        ))}
      </div>

      {crop === "corn" ? (
        <>
          <div style={{ ...acGrid(isMobile, 3), marginBottom: 16 }}>
            <AcStat k="Contest yield" v={`${cornFinal.toFixed(2)}`} sub="bu/acre at 15.5%" tone={cornAcres >= 1.25 ? "good" : "warn"} big />
            <AcStat k="Harvested area" v={`${cornAcres.toFixed(2)} ac`} sub={cornAcres >= 1.25 ? "meets 1.25 ac minimum" : "below 1.25 ac minimum"} tone={cornAcres >= 1.25 ? "neutral" : "bad"} />
            <AcStat k="Bushels weighed" v={cornBu.toFixed(2)} sub={`${fmt(acNum(cornLbs), 0)} lb at ${acNum(cornMoist).toFixed(2)}%`} />
          </div>

          {cornAcres > 0 && cornAcres < 1.25 && <AcNote tone="bad">Harvested area is under the 1.25-acre minimum — this plot would not qualify.</AcNote>}
          {needsSecond && secondVal == null && <AcNote tone="warn">First harvest is over 350 bu/acre, so a second contiguous 1.25 acres must be weighed. Enter it below to get the final averaged yield.</AcNote>}
          {secondVal != null && <AcNote>Final yield is the average of the two harvests: ({cornYield.toFixed(2)} + {secondVal.toFixed(2)}) ÷ 2 = <strong>{cornFinal.toFixed(2)}</strong> bu/acre.</AcNote>}

          {step(1, "Harvested area", (
            <>
              <div style={{ display: "flex", gap: 4, background: "#f0f4f0", borderRadius: 8, padding: 3, marginBottom: 12, maxWidth: 380 }}>
                {[{ k: "rows", l: "Rows × length" }, { k: "total", l: "Total row feet" }].map(o => (
                  <button key={o.k} onClick={() => setAreaMode(o.k)}
                    style={{ flex: 1, padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                      background: areaMode === o.k ? "#fff" : "transparent", color: areaMode === o.k ? "#1a2e1a" : "#60735f",
                      boxShadow: areaMode === o.k ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}>{o.l}</button>
                ))}
              </div>
              <div style={acGrid(isMobile, 3)}>
                <AcField label="Row spacing" value={rowSpacing} onChange={setRowSpacing} suffix="in" step="1" hint={`= ${rowWidthFt.toFixed(2)} ft`} />
                {areaMode === "rows" ? (
                  <>
                    <AcField label="Rows harvested" value={rowCount} onChange={setRowCount} suffix="rows" step="1" />
                    <AcField label="Row length" value={rowLength} onChange={setRowLength} suffix="ft" step="1" hint={`= ${fmt(effTotalRow, 0)} total row feet`} />
                  </>
                ) : (
                  <AcField label="Total row length" value={totalRowLen} onChange={setTotalRowLen} suffix="ft" step="1" hint="All rows added together" />
                )}
              </div>
            </>
          ), `${fmt(effTotalRow, 0)} ft × ${rowWidthFt.toFixed(2)} ft ÷ 43,560 = ${cornAcres.toFixed(2)} acres`)}

          {step(2, "Bushels at 15.5%", (
            <div style={acGrid(isMobile, 3)}>
              <AcField label="Total pounds" value={cornLbs} onChange={setCornLbs} suffix="lb" step="1" />
              <AcField label="Actual moisture" value={cornMoist} onChange={setCornMoist} suffix="%" step="0.01" />
            </div>
          ), `${fmt(acNum(cornLbs), 0)} × ${cornMoistAdj.toFixed(4)} ÷ 47.32 = ${cornBu.toFixed(2)} bu`)}

          {step(3, "Yield per acre", (
            <div style={acGrid(isMobile, 3)}>
              <AcField label="Second harvest" value={second} onChange={setSecond} suffix="bu/ac" hint="Required only if first > 350" />
            </div>
          ), `${cornBu.toFixed(2)} bu ÷ ${cornAcres.toFixed(2)} ac = ${cornYield.toFixed(2)} bu/acre`)}

          <AcCard title="Corn contest rules">
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#4a6e4a", lineHeight: 1.7 }}>
              <li>Minimum 1.25 acres harvested, from a field of at least 5 acres</li>
              <li>Over 350 bu/acre, a second contiguous 1.25 acres must be weighed and the two averaged</li>
              <li>Exclude headlands and outside rows</li>
              <li>Every step rounded to two decimals; standard moisture 15.5%</li>
            </ul>
          </AcCard>
        </>
      ) : (
        <>
          <div style={{ ...acGrid(isMobile, 3), marginBottom: 16 }}>
            <AcStat k="Contest yield" v={soyYield.toFixed(2)} sub="bu/acre at 13%" tone={soyAcres >= 2 ? "good" : "warn"} big />
            <AcStat k="Harvested area" v={`${soyAcres.toFixed(2)} ac`} sub={soyAcres >= 2 ? "meets 2 ac minimum" : "below 2 ac minimum"} tone={soyAcres >= 2 ? "neutral" : "bad"} />
            <AcStat k="Area weighed" v={`${fmt(soySqFt, 0)}`} sub="square feet" />
          </div>

          {soyAcres > 0 && soyAcres < 2 && <AcNote tone="bad">Harvested area is under the 2-acre minimum — this plot would not qualify.</AcNote>}

          {step(1, "Harvested area", (
            <div style={acGrid(isMobile, 3)}>
              <AcField label="Length" value={soyLen} onChange={setSoyLen} suffix="ft" step="1" />
              <AcField label="Width" value={soyWid} onChange={setSoyWid} suffix="ft" step="1" />
            </div>
          ), `${fmt(acNum(soyLen), 0)} × ${fmt(acNum(soyWid), 0)} = ${fmt(soySqFt, 0)} sq ft ÷ 43,560 = ${soyAcres.toFixed(2)} acres`)}

          {step(2, "Yield per acre", (
            <div style={acGrid(isMobile, 3)}>
              <AcField label="Total pounds" value={soyLbs} onChange={setSoyLbs} suffix="lb" step="1" />
              <AcField label="Grain moisture" value={soyMoist} onChange={setSoyMoist} suffix="%" step="0.01" />
            </div>
          ), `${fmt(acNum(soyLbs), 0)} × ${soyAdj.toFixed(4)} ÷ 52.2 ÷ ${soyAcres.toFixed(2)} = ${soyYield.toFixed(2)} bu/acre`)}

          <AcCard title="Soybean contest rules">
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#4a6e4a", lineHeight: 1.7 }}>
              <li>Minimum 2 acres harvested, from a field of at least 5 acres</li>
              <li>Harvested area must be contiguous and squared off</li>
              <li>Exclude headlands and outside rows</li>
              <li>Every step rounded to two decimals; standard moisture 13%</li>
            </ul>
          </AcCard>
        </>
      )}

      <div style={{ fontSize: 11, color: "#677167", lineHeight: 1.6 }}>
        Formulas are the NYC&amp;SGA contest forms. Verify against the official form before you submit — the judges' sheet is what counts.
      </div>
    </div>
  );
}

// ── LIQUID STARTER BLEND ─────────────────────────────────────────────────────
// Solve for gallons of each product on hand that hit a target N-P-K-S analysis.
//
// The website version ran non-linear gradient descent from 60 RANDOM starting
// points, so the same inputs could return a different recipe each press. This
// solves the same problem CONVEXLY and deterministically.
//
// The trick: "blend analysis = target" looks non-linear because it's a ratio
// (nutrient lb ÷ blend lb), but it rearranges to a linear constraint. For each
// nutrient k, with gallons gᵢ, density dᵢ and analysis aᵢₖ:
//
//     Σ gᵢ·dᵢ·aᵢₖ/100  =  (tₖ/100)·Σ gᵢ·dᵢ
//     ⟺  Σ gᵢ·dᵢ·(aᵢₖ − tₖ)/100  =  0
//
// which is linear in g. So we minimise ‖A·g‖² over the simplex {g ≥ 0,
// Σg = total} — a convex QP — by projected gradient with an exact Lipschitz
// step. Same answer every time, and it actually reaches the optimum instead of
// stopping wherever 400 iterations happened to land.
const BLEND_PRODUCTS = [
  { name: "10-34-0 (APP)",        n: 10, p: 34, k: 0,  s: 0,    density: 11.65 },
  { name: "11-37-0",              n: 11, p: 37, k: 0,  s: 0,    density: 11.80 },
  { name: "UAN 32-0-0",           n: 32, p: 0,  k: 0,  s: 0,    density: 11.06 },
  { name: "UAN 30-0-0",           n: 30, p: 0,  k: 0,  s: 0,    density: 10.86 },
  { name: "UAN 28-0-0",           n: 28, p: 0,  k: 0,  s: 0,    density: 10.67 },
  { name: "UAN 28-0-0-5.6S",      n: 28, p: 0,  k: 0,  s: 5.6,  density: 11.06 },
  { name: "ATS 12-0-0-26S",       n: 12, p: 0,  k: 0,  s: 26,   density: 11.10 },
  { name: "KTS 0-0-25-17S",       n: 0,  p: 0,  k: 25, s: 17,   density: 12.40 },
  { name: "0-0-30 (potassium)",   n: 0,  p: 0,  k: 30, s: 0,    density: 11.60 },
  { name: "9-18-9",               n: 9,  p: 18, k: 9,  s: 0,    density: 10.90 },
  { name: "Water",                n: 0,  p: 0,  k: 0,  s: 0,    density: 8.34  },
];
const BLEND_KEYS = ["n", "p", "k", "s"];

// Euclidean projection of v onto { x ≥ 0, Σx = total } (Duchi et al.).
function blendProjectSimplex(v, total) {
  const n = v.length;
  if (n === 0) return [];
  const u = [...v].sort((a, b) => b - a);
  let css = 0, theta = (u.reduce((a, b) => a + b, 0) - total) / n;
  for (let i = 0; i < n; i++) {
    css += u[i];
    const t = (css - total) / (i + 1);
    if (u[i] - t > 0) theta = t;
  }
  return v.map(x => Math.max(0, x - theta));
}

// Analysis of a candidate recipe: blend weight and the four nutrient percentages.
function blendAnalysis(g, products) {
  let weight = 0;
  g.forEach((gi, i) => { weight += gi * products[i].density; });
  const pct = BLEND_KEYS.map(key => {
    let lbs = 0;
    g.forEach((gi, i) => { lbs += gi * products[i].density * ((products[i][key] || 0) / 100); });
    return weight > 0 ? lbs / weight * 100 : 0;
  });
  return { weight, pct };
}
// Worst nutrient miss, in analysis points. Nutrients targeted at ZERO count:
// a blend that drags in 5% unwanted sulfur is off-target, not on it.
function blendMaxErr(g, products, targets) {
  const a = blendAnalysis(g, products);
  if (!(a.weight > 0)) return Infinity;
  let e = 0;
  BLEND_KEYS.forEach((key, i) => { e = Math.max(e, Math.abs(a.pct[i] - (targets[key] || 0))); });
  return e;
}

// One projected-gradient pass at a fixed cost weight. `cw` = 0 solves for pure
// analysis accuracy; positive values trade a little accuracy for a cheaper mix.
function blendRun(products, targets, totalGal, costPerGal, cw) {
  const np = products.length;
  // A[k][i] = dᵢ·(aᵢₖ − tₖ)/100 — residual contribution of one gallon of i.
  const A = BLEND_KEYS.map(key => products.map(pr => pr.density * ((pr[key] || 0) - (targets[key] || 0)) / 100));
  let lip = 0;
  A.forEach(row => row.forEach(a => { lip += a * a; }));
  lip = Math.max(2 * lip, 1e-9);              // 2‖A‖_F² upper-bounds the gradient's Lipschitz constant
  let g = new Array(np).fill(totalGal / np);
  for (let iter = 0; iter < 6000; iter++) {
    const resid = A.map(row => { let s = 0; for (let i = 0; i < np; i++) s += row[i] * g[i]; return s; });
    const grad = new Array(np).fill(0);
    for (let ki = 0; ki < A.length; ki++) for (let i = 0; i < np; i++) grad[i] += 2 * A[ki][i] * resid[ki];
    if (cw > 0) for (let i = 0; i < np; i++) grad[i] += cw * costPerGal[i];
    const next = blendProjectSimplex(g.map((x, i) => x - grad[i] / lip), totalGal);
    let moved = 0;
    for (let i = 0; i < np; i++) moved += Math.abs(next[i] - g[i]);
    g = next;
    if (moved < 1e-10 * totalGal) break;
  }
  return { g, lip };
}

// products: [{n,p,k,s,density}], targets: {n,p,k,s}, costPerGal: number[].
//
// Accuracy first, price second, and in that order — a single blended objective
// gets this wrong. Solving accuracy and price together at one weight either
// leaves money on the table (weight too low) or quietly misses the analysis
// (weight too high), and the user can't tell which. So: solve for the best
// achievable analysis, then sweep cost weights and keep the CHEAPEST recipe
// still within 0.02 points of it. On a 14-0-0 out of UAN28 at $300 and UAN32 at
// $520 that is the difference between a $351 batch and a $434 one, at 0.02
// points of analysis.
function blendSolve(products, targets, totalGal, costPerGal, preferCheap) {
  if (!products.length || totalGal <= 0) return [];
  const base = blendRun(products, targets, totalGal, costPerGal, 0);
  let best = base.g;
  const maxCost = Math.max(...costPerGal.map(c => Math.abs(c)));
  if (!preferCheap || !(maxCost > 0)) return best;

  const tol = blendMaxErr(best, products, targets) + 0.02;
  const totalCost = (g) => g.reduce((s, gi, i) => s + gi * costPerGal[i], 0);
  let bestCost = totalCost(best);
  for (let k = -5; k <= 2; k += 0.5) {
    const cw = Math.pow(10, k) * base.lip * totalGal / maxCost;
    const cand = blendRun(products, targets, totalGal, costPerGal, cw).g;
    if (blendMaxErr(cand, products, targets) <= tol) {
      const c = totalCost(cand);
      if (c < bestCost - 1e-9) { bestCost = c; best = cand; }
    }
  }
  return best;
}

function StarterBlendCalc({ isMobile }) {
  const [tgt, setTgt]         = useState({ n: "17", p: "17", k: "0", s: "0" });
  const [totalGal, setTotalGal] = useState("500");
  const [appRate, setAppRate] = useState("24");
  const [preferCheap, setPreferCheap] = useState(true);
  const [custom, setCustom]   = useState([]);
  const [draft, setDraft]     = useState({ name: "", n: "", p: "", k: "", s: "", density: "" });
  // Per-product UI state, keyed by index into `products`.
  const [picked, setPicked]   = useState({ 0: true, 2: true, 10: true });
  const [prices, setPrices]   = useState({});
  const [densities, setDensities] = useState({});

  const products = useMemo(() => [...BLEND_PRODUCTS, ...custom], [custom]);
  const density = (i) => acNumOrNull(densities[i]) ?? products[i].density;

  const selIdx = products.map((_, i) => i).filter(i => picked[i]);
  const selProducts = selIdx.map(i => ({ ...products[i], density: density(i) }));
  const selPrices   = selIdx.map(i => acNum(prices[i]));
  const targets = { n: acNum(tgt.n), p: acNum(tgt.p), k: acNum(tgt.k), s: acNum(tgt.s) };
  const gal = acNum(totalGal);

  const solution = useMemo(() => {
    if (!selProducts.length || gal <= 0) return null;
    const costPerGal = selProducts.map((pr, i) => selPrices[i] / 2000 * pr.density);
    const g = blendSolve(selProducts, targets, gal, costPerGal, preferCheap);
    let cost = 0;
    g.forEach((gi, i) => { cost += gi * costPerGal[i]; });
    const a = blendAnalysis(g, selProducts);
    return { g, weight: a.weight, cost, pct: a.pct, err: blendMaxErr(g, selProducts, targets) };
    // selProducts/selPrices are rebuilt every render from the state below, so
    // serialised primitives — not the derived arrays — are what decide the answer.
  }, [JSON.stringify(selIdx), JSON.stringify(selPrices), JSON.stringify(densities), JSON.stringify(tgt), gal, preferCheap, custom.length]);

  const maxErr = solution ? solution.err : 0;
  const hasPrice = selPrices.some(p => p > 0);
  const blendDensity = solution && gal > 0 ? solution.weight / gal : 0;
  const costPerGalOut = solution && gal > 0 ? solution.cost / gal : 0;
  const rate = acNum(appRate);

  const addCustom = () => {
    const name = (draft.name || "").trim();
    if (!name) return;
    setCustom(c => [...c, { name, n: acNum(draft.n), p: acNum(draft.p), k: acNum(draft.k), s: acNum(draft.s), density: acNum(draft.density) || 10.67 }]);
    setPicked(pk => ({ ...pk, [products.length]: true }));
    setDraft({ name: "", n: "", p: "", k: "", s: "", density: "" });
  };

  const grade = solution
    ? `${Math.round(solution.pct[0])}-${Math.round(solution.pct[1])}-${Math.round(solution.pct[2])}${solution.pct[3] >= 0.5 ? `-${Math.round(solution.pct[3])}S` : ""}`
    : "—";
  const th = { textAlign: "right", padding: "6px 8px", fontSize: 10, fontWeight: 800, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e4ece4" };
  const td = { textAlign: "right", padding: "7px 8px", fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: "#1a2e1a", borderBottom: "1px solid #f2f6f2" };
  const smallInp = { width: 62, padding: "5px 7px", borderRadius: 5, border: "1px solid #d0e0d0", fontSize: 12, fontVariantNumeric: "tabular-nums", textAlign: "right", boxSizing: "border-box" };

  return (
    <div>
      <AcCard title="Target analysis" sub="What you want the finished blend to test.">
        <div style={{ ...acGrid(isMobile, 5), alignItems: "end" }}>
          <AcField label="%N" value={tgt.n} onChange={v => setTgt(t => ({ ...t, n: v }))} step="0.5" />
          <AcField label="%P₂O₅" value={tgt.p} onChange={v => setTgt(t => ({ ...t, p: v }))} step="0.5" />
          <AcField label="%K₂O" value={tgt.k} onChange={v => setTgt(t => ({ ...t, k: v }))} step="0.5" />
          <AcField label="%S" value={tgt.s} onChange={v => setTgt(t => ({ ...t, s: v }))} step="0.1" />
          <AcField label="Batch size" value={totalGal} onChange={setTotalGal} suffix="gal" step="50" />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 12.5, color: "#4a6e4a", cursor: "pointer" }}>
          <input type="checkbox" checked={preferCheap} onChange={e => setPreferCheap(e.target.checked)} />
          When more than one recipe hits the target, pick the cheapest
        </label>
      </AcCard>

      <AcCard title="Products on hand" sub="Check what you have. Price is optional — it only drives the cost columns.">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: "left", width: 34 }}></th>
              <th style={{ ...th, textAlign: "left" }}>Product</th>
              <th style={th}>Price</th>
              <th style={th}>Density</th>
            </tr></thead>
            <tbody>
              {products.map((pr, i) => {
                const on = !!picked[i];
                const analysis = `${pr.n}-${pr.p}-${pr.k}${pr.s > 0 ? `-${pr.s}S` : ""}`;
                return (
                  <tr key={`${pr.name}-${i}`} style={on ? { background: "#f4faf4" } : undefined}>
                    <td style={{ ...td, textAlign: "center" }}>
                      <input type="checkbox" checked={on} onChange={e => setPicked(pk => ({ ...pk, [i]: e.target.checked }))} />
                    </td>
                    <td style={{ ...td, textAlign: "left", fontFamily: "inherit" }}>
                      <div style={{ fontWeight: on ? 700 : 500, fontSize: 13 }}>{pr.name}</div>
                      <div style={{ fontSize: 10.5, color: "#677167", fontVariantNumeric: "tabular-nums" }}>{analysis}</div>
                    </td>
                    <td style={td}>
                      <input type="number" min="0" step="1" placeholder="—" value={prices[i] ?? ""}
                        onChange={e => setPrices(p => ({ ...p, [i]: e.target.value }))} style={smallInp} />
                      <span style={{ fontSize: 10, color: "#677167", marginLeft: 4 }}>$/ton</span>
                    </td>
                    <td style={td}>
                      <input type="number" min="1" step="0.01" value={densities[i] ?? pr.density}
                        onChange={e => setDensities(d => ({ ...d, [i]: e.target.value }))} style={smallInp} />
                      <span style={{ fontSize: 10, color: "#677167", marginLeft: 4 }}>lb/gal</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 12, paddingTop: 12, borderTop: "1px solid #f0f4f0" }}>
          <input placeholder="Custom product name" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            style={{ ...smallInp, width: 170, textAlign: "left", fontFamily: "inherit" }} />
          {[["n", "%N"], ["p", "%P"], ["k", "%K"], ["s", "%S"], ["density", "lb/gal"]].map(([f, ph]) => (
            <input key={f} type="number" placeholder={ph} value={draft[f]} onChange={e => setDraft(d => ({ ...d, [f]: e.target.value }))} style={{ ...smallInp, width: 68 }} />
          ))}
          <button onClick={addCustom} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#2d6a2d", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>
        </div>
      </AcCard>

      {!solution ? (
        <AcNote tone="warn">Check at least one product to solve a recipe.</AcNote>
      ) : (
        <>
          {maxErr > 1.0 && <AcNote tone="bad">These products can't reach that analysis — the closest achievable blend is shown, off by up to {maxErr.toFixed(1)} points. Add a product that carries the nutrient you're short on.</AcNote>}
          {maxErr > 0.3 && maxErr <= 1.0 && <AcNote tone="warn">Close — within {maxErr.toFixed(1)} points of target. The gap is product chemistry, not solver error.</AcNote>}

          <div style={{ ...acGrid(isMobile, 4), marginBottom: 16 }}>
            <AcStat k="Blend grade" v={grade} sub={`${blendDensity.toFixed(2)} lb/gal`} tone={maxErr <= 0.3 ? "good" : maxErr <= 1 ? "warn" : "bad"} big />
            {BLEND_KEYS.map((key, i) => {
              const t = targets[key];
              const diff = Math.abs(solution.pct[i] - t);
              // A nutrient targeted at zero still flags when the mix drags it in.
              const tone = diff > 1 ? "bad" : diff > 0.3 ? "warn" : (t > 0 ? "good" : "neutral");
              return <AcStat key={key} k={["Nitrogen", "Phosphate", "Potash", "Sulfur"][i]} v={`${solution.pct[i].toFixed(1)}%`}
                sub={t > 0 ? `target ${t}% · ${(solution.pct[i] * 20).toFixed(1)} lb/ton` : `${(solution.pct[i] * 20).toFixed(1)} lb/ton`} tone={tone} />;
            })}
          </div>

          <AcCard title="Recipe" sub={`${fmt(gal, 0)} gallon batch`}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
                <thead><tr>
                  <th style={{ ...th, textAlign: "left" }}>Product</th>
                  <th style={th}>Gallons</th>
                  <th style={th}>Pounds</th>
                  <th style={th}>% of mix</th>
                  {hasPrice && <th style={th}>Cost</th>}
                </tr></thead>
                <tbody>
                  {selProducts.map((pr, i) => {
                    if (solution.g[i] < 0.05) return null;
                    const lbs = solution.g[i] * pr.density;
                    const lineCost = solution.g[i] * (selPrices[i] / 2000 * pr.density);
                    return (
                      <tr key={`${pr.name}-${i}`}>
                        <td style={{ ...td, textAlign: "left", fontFamily: "inherit", fontWeight: 600 }}>{pr.name}</td>
                        <td style={td}>{solution.g[i].toFixed(1)}</td>
                        <td style={td}>{fmt(lbs, 0)}</td>
                        <td style={td}>{solution.weight > 0 ? (lbs / solution.weight * 100).toFixed(1) : "0.0"}%</td>
                        {hasPrice && <td style={td}>{selPrices[i] > 0 ? fmtD(lineCost) : "—"}</td>}
                      </tr>
                    );
                  })}
                  <tr style={{ background: "#f4fbf2", fontWeight: 800 }}>
                    <td style={{ ...td, textAlign: "left", fontFamily: "inherit" }}>Total</td>
                    <td style={td}>{fmt(gal, 0)}</td>
                    <td style={td}>{fmt(solution.weight, 0)}</td>
                    <td style={td}>100%</td>
                    {hasPrice && <td style={{ ...td, color: "#1f6f3a" }}>{fmtD(solution.cost)}</td>}
                  </tr>
                </tbody>
              </table>
            </div>
          </AcCard>

          <AcCard title="Application rate">
            <div style={{ ...acGrid(isMobile, hasPrice ? 6 : 5), alignItems: "end" }}>
              <AcField label="Apply at" value={appRate} onChange={setAppRate} suffix="gal/ac" step="0.5" />
              {BLEND_KEYS.map((key, i) => (
                <AcStat key={key} k={["N lb/ac", "P₂O₅ lb/ac", "K₂O lb/ac", "S lb/ac"][i]}
                  v={(rate * blendDensity * solution.pct[i] / 100).toFixed(1)} />
              ))}
              {hasPrice && <AcStat k="$/acre" v={fmtD(rate * costPerGalOut)} tone="good" />}
            </div>
            <div style={{ fontSize: 11, color: "#677167", marginTop: 10 }}>
              {fmt(gal, 0)} gallons covers {blendDensity > 0 && rate > 0 ? fmt(gal / rate, 1) : "—"} acres at this rate.
            </div>
          </AcCard>
        </>
      )}

      <div style={{ fontSize: 11, color: "#677167", lineHeight: 1.6 }}>
        Check compatibility and jar-test any new combination before mixing a full batch — this solves the analysis, not the chemistry.
      </div>
    </div>
  );
}

// ── CATTLE RATION & COST OF GAIN ─────────────────────────────────────────────
// Derives cost of gain ($/lb) from an actual ration instead of asking for it as
// a guess. That number is the single biggest input to LRPProfitView, which up
// to now made you type it — so the result hands off there.
//
// Deliberately a COST tool, not a nutrition model: it does not balance protein,
// energy or minerals. It answers "given what I'm feeding and what it costs,
// what does a pound of gain cost me" — the closeout question.
//
// Yardage and vet/med are included because a closeout counts them against gain.
// Interest and purchase cost are NOT — they are financing and cattle cost, and
// they belong to the breakeven in LRP Profit Calc. Double-counting them here is
// how a cost of gain ends up 30¢ high.
const RATION_FEEDS = [
  { key: "corn",       name: "Shelled corn",        unit: "bu",  lbPerUnit: 56,   dm: 85, price: "4.30" },
  { key: "hmc",        name: "High-moisture corn",  unit: "ton", lbPerUnit: 2000, dm: 72, price: "150" },
  { key: "silage",     name: "Corn silage",         unit: "ton", lbPerUnit: 2000, dm: 35, price: "55" },
  { key: "haylage",    name: "Haylage / baleage",   unit: "ton", lbPerUnit: 2000, dm: 40, price: "70" },
  { key: "hay",        name: "Dry hay",             unit: "ton", lbPerUnit: 2000, dm: 88, price: "180" },
  { key: "ddg",        name: "Distillers grains",   unit: "ton", lbPerUnit: 2000, dm: 90, price: "210" },
  { key: "sbm",        name: "Soybean meal",        unit: "ton", lbPerUnit: 2000, dm: 89, price: "340" },
  { key: "midds",      name: "Wheat midds",         unit: "ton", lbPerUnit: 2000, dm: 89, price: "170" },
  { key: "cottonseed", name: "Whole cottonseed",    unit: "ton", lbPerUnit: 2000, dm: 90, price: "290" },
  { key: "mineral",    name: "Mineral / supplement", unit: "ton", lbPerUnit: 2000, dm: 98, price: "600" },
];

// Starting rations, lb AS FED per head per day. Backgrounding-through-finishing
// shape for a Northeast lot; every cell is overridable.
const RATION_PHASE_DEFAULTS = [
  { name: "Starter",  days: "45",  adg: "2.20", amounts: { corn: 6,  silage: 20, haylage: 8, ddg: 3, mineral: 0.4 } },
  { name: "Grower",   days: "90",  adg: "2.90", amounts: { corn: 12, silage: 26, haylage: 6, ddg: 4, mineral: 0.4 } },
  { name: "Finisher", days: "110", adg: "3.30", amounts: { corn: 20, silage: 22, haylage: 3, ddg: 5, mineral: 0.5 } },
];

// Where the ration tool leaves its answer for LRP Profit Calc to pick up. A
// plain sessionStorage handoff rather than lifted state: the two tools live on
// different screens, and a null read just means "nothing computed yet".
const RATION_COG_KEY = "silas.rationCog";
function readRationCog() {
  try {
    const raw = window.sessionStorage.getItem(RATION_COG_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return (v && Number.isFinite(v.cog) && v.cog > 0) ? v : null;
  } catch (e) { return null; }
}

function RationCogCalc({ isMobile }) {
  const [feeds, setFeeds]   = useState(() => RATION_FEEDS.map(f => ({ ...f })));
  const [phases, setPhases] = useState(() => RATION_PHASE_DEFAULTS.map(p => ({ ...p, amounts: { ...p.amounts } })));
  const [yardage, setYardage] = useState("0.35");
  const [vetPerHead, setVetPerHead] = useState("28");
  const [inWt, setInWt] = useState("550");
  const [saved, setSaved] = useState(false);

  const setFeedPrice = (i, v) => setFeeds(fs => fs.map((f, j) => j === i ? { ...f, price: v } : f));
  const setFeedDm    = (i, v) => setFeeds(fs => fs.map((f, j) => j === i ? { ...f, dm: v } : f));
  const setPhase     = (i, patch) => setPhases(ps => ps.map((p, j) => j === i ? { ...p, ...patch } : p));
  const setAmount    = (i, key, v) => setPhases(ps => ps.map((p, j) => j === i ? { ...p, amounts: { ...p.amounts, [key]: v } } : p));

  const pricePerLb = (f) => { const per = acNum(f.lbPerUnit); return per > 0 ? acNum(f.price) / per : 0; };

  // Per-phase economics. `cost` excludes yardage/vet so the split stays legible;
  // they're layered on at the total.
  const calc = phases.map(ph => {
    const days = acNum(ph.days);
    const adg  = acNum(ph.adg);
    let asFed = 0, dmLb = 0, feedCost = 0;
    feeds.forEach(f => {
      const amt = acNum(ph.amounts[f.key]);
      if (amt <= 0) return;
      asFed += amt;
      dmLb  += amt * acNum(f.dm) / 100;
      feedCost += amt * pricePerLb(f);
    });
    return { days, adg, asFed, dmLb, feedCost, gain: days * adg, totalFeed: days * feedCost };
  });

  const totalDays = calc.reduce((s, c) => s + c.days, 0);
  const totalGain = calc.reduce((s, c) => s + c.gain, 0);
  const totalFeedCost = calc.reduce((s, c) => s + c.totalFeed, 0);
  const yardageTotal = acNum(yardage) * totalDays;
  const vetTotal = acNum(vetPerHead);
  const totalCogCost = totalFeedCost + yardageTotal + vetTotal;
  const cog = totalGain > 0 ? totalCogCost / totalGain : 0;
  const feedOnlyCog = totalGain > 0 ? totalFeedCost / totalGain : 0;
  const outWt = acNum(inWt) + totalGain;
  const overallAdg = totalDays > 0 ? totalGain / totalDays : 0;
  const totalDm = calc.reduce((s, c) => s + c.dmLb * c.days, 0);
  const feedConv = totalGain > 0 ? totalDm / totalGain : 0;

  const sendToLrp = () => {
    if (!(cog > 0)) return;
    try {
      window.sessionStorage.setItem(RATION_COG_KEY, JSON.stringify({
        cog: Math.round(cog * 1000) / 1000,
        adg: Math.round(overallAdg * 100) / 100,
        days: totalDays, inWt: acNum(inWt), outWt: Math.round(outWt),
        at: new Date().toISOString(),
      }));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 4000);
    } catch (e) { /* private-mode storage — the number is still on screen */ }
  };

  const th = { textAlign: "right", padding: "6px 7px", fontSize: 10, fontWeight: 800, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #e4ece4" };
  const td = { textAlign: "right", padding: "5px 7px", fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: "#1a2e1a", borderBottom: "1px solid #f2f6f2" };
  const cellInp = { width: 60, padding: "5px 6px", borderRadius: 5, border: "1px solid #d0e0d0", fontSize: 12, fontVariantNumeric: "tabular-nums", textAlign: "right", boxSizing: "border-box" };

  return (
    <div>
      <AcNote>
        Cost of gain only — feed, yardage and vet/med against pounds put on. Purchase cost, interest and death loss belong to the
        breakeven, so they live in <strong>Quoters → LRP Profit Calc</strong>. Send this number over when you're done.
      </AcNote>

      <div style={{ ...acGrid(isMobile, 4), marginBottom: 16 }}>
        <AcStat k="Cost of gain" v={`${fmtD(cog)}/lb`} sub={`feed alone ${fmtD(feedOnlyCog)}/lb`} tone="good" big />
        <AcStat k="Days on feed" v={fmt(totalDays, 0)} sub={`${overallAdg.toFixed(2)} lb/day average`} />
        <AcStat k="Total gain" v={`${fmt(totalGain, 0)} lb`} sub={`${fmt(acNum(inWt), 0)} → ${fmt(outWt, 0)} lb`} />
        <AcStat k="Feed conversion" v={feedConv > 0 ? feedConv.toFixed(2) : "—"} sub="lb dry matter per lb gain" />
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <button onClick={sendToLrp} disabled={!(cog > 0)}
          style={{ padding: "9px 16px", borderRadius: 7, border: "none", background: cog > 0 ? "#2d6a2d" : "#c8d8c0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: cog > 0 ? "pointer" : "default", fontFamily: "inherit" }}>
          Send {fmtD(cog)}/lb to LRP Profit Calc
        </button>
        {saved && <span style={{ fontSize: 12.5, color: "#1f6f3a", fontWeight: 700 }}>✓ Saved — open Quoters → LRP Profit Calc to use it.</span>}
      </div>

      <AcCard title="Feed prices" sub="Price as you buy it. Dry matter drives the conversion figure, not the cost.">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: "left" }}>Feed</th>
              <th style={th}>Price</th>
              <th style={th}>Unit</th>
              <th style={th}>$/lb as fed</th>
              <th style={th}>% DM</th>
              <th style={th}>$/lb DM</th>
            </tr></thead>
            <tbody>
              {feeds.map((f, i) => {
                const ppl = pricePerLb(f);
                const dm = acNum(f.dm);
                return (
                  <tr key={f.key}>
                    <td style={{ ...td, textAlign: "left", fontFamily: "inherit", fontWeight: 600 }}>{f.name}</td>
                    <td style={td}><input type="number" min="0" step="0.01" value={f.price} onChange={e => setFeedPrice(i, e.target.value)} style={cellInp} /></td>
                    <td style={{ ...td, color: "#677167", fontSize: 11 }}>$/{f.unit}</td>
                    <td style={td}>{ppl > 0 ? `$${ppl.toFixed(4)}` : "—"}</td>
                    <td style={td}><input type="number" min="1" max="100" step="1" value={f.dm} onChange={e => setFeedDm(i, e.target.value)} style={cellInp} /></td>
                    <td style={td}>{ppl > 0 && dm > 0 ? `$${(ppl / (dm / 100)).toFixed(4)}` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </AcCard>

      <AcCard title="Ration by phase" sub="Pounds AS FED per head per day. Leave a feed blank when it isn't in that phase.">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: "left" }}>Feed</th>
              {phases.map((ph, i) => <th key={i} style={th}>{ph.name}</th>)}
            </tr></thead>
            <tbody>
              {feeds.map(f => (
                <tr key={f.key}>
                  <td style={{ ...td, textAlign: "left", fontFamily: "inherit" }}>{f.name}</td>
                  {phases.map((ph, i) => (
                    <td key={i} style={td}>
                      <input type="number" min="0" step="0.1" placeholder="—" value={ph.amounts[f.key] ?? ""}
                        onChange={e => setAmount(i, f.key, e.target.value)} style={cellInp} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ background: "#f7faf7" }}>
                <td style={{ ...td, textAlign: "left", fontFamily: "inherit", fontWeight: 700 }}>Days in phase</td>
                {phases.map((ph, i) => (
                  <td key={i} style={td}><input type="number" min="0" step="1" value={ph.days} onChange={e => setPhase(i, { days: e.target.value })} style={cellInp} /></td>
                ))}
              </tr>
              <tr style={{ background: "#f7faf7" }}>
                <td style={{ ...td, textAlign: "left", fontFamily: "inherit", fontWeight: 700 }}>Rate of gain (lb/day)</td>
                {phases.map((ph, i) => (
                  <td key={i} style={td}><input type="number" min="0" step="0.05" value={ph.adg} onChange={e => setPhase(i, { adg: e.target.value })} style={cellInp} /></td>
                ))}
              </tr>
              <tr style={{ background: "#eef7ee", fontWeight: 800 }}>
                <td style={{ ...td, textAlign: "left", fontFamily: "inherit" }}>Feed cost / head / day</td>
                {calc.map((c, i) => <td key={i} style={{ ...td, color: "#1f6f3a" }}>{fmtD(c.feedCost)}</td>)}
              </tr>
              <tr>
                <td style={{ ...td, textAlign: "left", fontFamily: "inherit", color: "#60735f" }}>Dry matter intake (lb/day)</td>
                {calc.map((c, i) => <td key={i} style={{ ...td, color: "#60735f" }}>{c.dmLb.toFixed(1)}</td>)}
              </tr>
              <tr>
                <td style={{ ...td, textAlign: "left", fontFamily: "inherit", color: "#60735f" }}>Cost of gain in phase</td>
                {calc.map((c, i) => <td key={i} style={{ ...td, color: "#60735f" }}>{c.adg > 0 ? `${fmtD((c.feedCost + acNum(yardage)) / c.adg)}/lb` : "—"}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </AcCard>

      <AcCard title="Other cost of gain">
        <div style={acGrid(isMobile, 3)}>
          <AcField label="Yardage / overhead" value={yardage} onChange={setYardage} suffix="$/hd/day" hint={`${fmtD(yardageTotal)} over ${fmt(totalDays, 0)} days`} />
          <AcField label="Vet & med" value={vetPerHead} onChange={setVetPerHead} suffix="$/hd" step="1" hint="Processing, implants, treatment" />
          <AcField label="Weight in" value={inWt} onChange={setInWt} suffix="lb" step="5" hint="Only used to show the finished weight" />
        </div>
      </AcCard>

      <AcCard title="Where the cost of gain comes from">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[
            ["Feed", totalFeedCost],
            [`Yardage (${fmtD(acNum(yardage))}/day × ${fmt(totalDays, 0)})`, yardageTotal],
            ["Vet & med", vetTotal],
          ].map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#4a6e4a", padding: "6px 0", borderBottom: "1px solid #f2f6f2" }}>
              <span>{r[0]}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#1a2e1a" }}>
                {fmtD(r[1])}/hd{totalGain > 0 ? ` · ${fmtD(r[1] / totalGain)}/lb` : ""}
              </span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, fontWeight: 800, color: "#1f6f3a", paddingTop: 8 }}>
            <span>Cost of gain</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtD(totalCogCost)}/hd · {fmtD(cog)}/lb</span>
          </div>
        </div>
      </AcCard>

      <div style={{ fontSize: 11, color: "#677167", lineHeight: 1.6 }}>
        A cost tool, not a ration balancer — it does not check protein, energy or mineral adequacy. Have a nutritionist balance the ration;
        use this to price it.
      </div>
    </div>
  );
}

// ── AG CALCULATORS HUB ───────────────────────────────────────────────────────
function AgCalcHub({ isMobile, mode = "admin" }) {
  const tabs = [
    { key: "hmcorn",  label: "High-Moisture Corn", icon: "🌽" },
    { key: "ration",  label: "Ration & Cost of Gain", icon: "🐄" },
    { key: "blend",   label: "Starter Blend", icon: "🧪" },
    { key: "contest", label: "Yield Contest", icon: "🏆" },
  ];
  const [tab, setTab] = useState("hmcorn");
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        <h1 style={{ ...uiPageTitle(isMobile) }}>Ag Calculators</h1>
        <div style={{ fontSize: 12.5, color: "#60735f", marginTop: 3 }}>
          Field and feed math{mode === "portal" ? "" : " — the tools that used to live on cloverag.com"}.
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid #e4ece4", marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={hubTabStyle(tab === t.key, isMobile)}>
            <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>
      {tab === "hmcorn"  && <HMCornCalc isMobile={isMobile} />}
      {tab === "ration"  && <RationCogCalc isMobile={isMobile} />}
      {tab === "blend"   && <StarterBlendCalc isMobile={isMobile} />}
      {tab === "contest" && <YieldContestCalc isMobile={isMobile} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DMC — Dairy Margin Coverage monthly margins (USDA FSA, scraped → dmc_margins)
// margin = U.S. All-Milk price − DMC feed cost. DMC pays the elected coverage
// level ($4.00–$9.50/cwt) minus the actual margin, whenever the margin is lower.
// ─────────────────────────────────────────────────────────────────────────────
function DMCView({ isMobile }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coverage, setCoverage] = useState(9.5); // Tier 1 max — the usual headline

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await sbFetch("dmc_margins", {
          select: "year,month,corn_price,alfalfa_hay,soybean_meal,all_milk_price,feed_cost,margin,updated_at",
          order: "year.desc,month.desc",
        });
        if (alive) setRows(data || []);
      } catch (e) {
        if (alive) setError(e.message);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const COVERAGE_LEVELS = [];
  for (let c = 9.5; c >= 4.0; c -= 0.5) COVERAGE_LEVELS.push(Number(c.toFixed(2)));

  // rows arrive year.desc,month.desc → most-recent first
  const withMargin = rows.filter((r) => r.margin != null);
  const latest = withMargin[0] || null;
  const trailing12 = withMargin.slice(0, 12);
  const below12 = trailing12.filter((r) => Number(r.margin) < coverage).length;
  const payoutLatest = latest ? Math.max(0, coverage - Number(latest.margin)) : 0;

  const years = [...new Set(rows.map((r) => r.year))].sort((a, b) => b - a);
  const byYM = {};
  rows.forEach((r) => { byYM[`${r.year}-${r.month}`] = r; });

  // chart series: last 36 months, ascending
  const series = withMargin.slice(0, 36).reverse();

  const card = (label, value, sub, accent) => (
    <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: isMobile ? "12px 14px" : "16px 18px", flex: 1, minWidth: isMobile ? 130 : 160 }}>
      <div style={{ fontSize: 11, color: "#60735f", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: accent || "#1a2e1a", fontFamily: "'Lora','Georgia',serif", marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#60735f", marginTop: 2 }}>{sub}</div>}
    </div>
  );

  // ── inline SVG margin chart ──
  function MarginChart() {
    if (series.length < 2) return null;
    const W = 720, H = 200, padL = 40, padR = 12, padT = 14, padB = 24;
    const vals = series.map((r) => Number(r.margin));
    const yMax = Math.ceil(Math.max(coverage, ...vals) + 1);
    const yMin = Math.floor(Math.min(0, ...vals));
    const x = (i) => padL + (i * (W - padL - padR)) / (series.length - 1);
    const y = (v) => padT + ((yMax - v) * (H - padT - padB)) / (yMax - yMin);
    const linePts = series.map((r, i) => `${x(i)},${y(Number(r.margin))}`).join(" ");
    const covY = y(coverage);
    const yTicks = [];
    for (let t = yMin; t <= yMax; t += Math.ceil((yMax - yMin) / 5) || 1) yTicks.push(t);
    return (
      <div style={{ width: "100%", overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 480, height: "auto", display: "block" }}>
          {yTicks.map((t) => (
            <g key={t}>
              <line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke="#eef3ee" strokeWidth="1" />
              <text x={padL - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill="#60735f">${t}</text>
            </g>
          ))}
          {/* coverage threshold */}
          <line x1={padL} y1={covY} x2={W - padR} y2={covY} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5 4" />
          <text x={W - padR} y={covY - 4} textAnchor="end" fontSize="10" fill="#c0392b" fontWeight="700">coverage ${coverage.toFixed(2)}</text>
          {/* margin line */}
          <polyline points={linePts} fill="none" stroke="#2d4a2d" strokeWidth="2" />
          {series.map((r, i) => {
            const below = Number(r.margin) < coverage;
            return <circle key={i} cx={x(i)} cy={y(Number(r.margin))} r={below ? 3.2 : 2.4} fill={below ? "#c0392b" : "#4a6e4a"} />;
          })}
          {/* x labels: first, mid, last */}
          {[0, Math.floor((series.length - 1) / 2), series.length - 1].map((i) => (
            <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#60735f">
              {MONTH_ABBR[series[i].month - 1]} '{String(series[i].year).slice(2)}
            </text>
          ))}
        </svg>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 18 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 18, fontWeight: 700, color: "#1a2e1a" }}>
          Dairy Margin Coverage
        </div>
        <div style={{ fontSize: 13, color: "#60735f", marginTop: 2, maxWidth: 720 }}>
          DMC margin = U.S. All-Milk price − DMC feed cost (corn, soybean meal, premium alfalfa hay).
          DMC pays your elected coverage level minus the actual margin, for every month the margin falls below it.
        </div>
      </div>

      {loading && <div style={{ color: "#60735f", padding: 20 }}>Loading DMC margins…</div>}
      {error && <div style={{ color: "#c0392b", padding: 20 }}>Error loading DMC data: {error}</div>}

      {!loading && !error && (
        <>
          {/* coverage selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#2d4a2d" }}>Coverage level:</span>
            <select
              value={coverage}
              onChange={(e) => setCoverage(Number(e.target.value))}
              style={{ padding: "6px 10px", border: "1px solid #c5d6c5", borderRadius: 8, fontSize: 14, color: "#1a2e1a", background: "#fff" }}
            >
              {COVERAGE_LEVELS.map((c) => (
                <option key={c} value={c}>${c.toFixed(2)} / cwt</option>
              ))}
            </select>
            <span style={{ fontSize: 12, color: "#60735f" }}>red = margin below this level (DMC pays)</span>
          </div>

          {/* KPI cards */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
            {card(
              "Latest margin",
              latest ? fmtD(latest.margin) : "—",
              latest ? `${MONTH_ABBR[latest.month - 1]} ${latest.year}` : null,
              latest && Number(latest.margin) < coverage ? "#c0392b" : "#2d6a1a"
            )}
            {card(
              `Status at $${coverage.toFixed(2)}`,
              latest ? (payoutLatest > 0 ? "Paying" : "No payment") : "—",
              latest && payoutLatest > 0 ? `${fmtD(payoutLatest)}/cwt indemnity` : "margin above coverage",
              latest && payoutLatest > 0 ? "#c0392b" : "#2d6a1a"
            )}
            {card("Months paid (last 12)", `${below12} / ${trailing12.length}`, `below $${coverage.toFixed(2)}`, below12 > 0 ? "#c0392b" : "#2d6a1a")}
            {latest && card("Latest All-Milk", fmtD(latest.all_milk_price), `feed cost ${fmtD(latest.feed_cost)}`)}
          </div>

          {/* chart */}
          <div style={{ background: "#fafdfa", border: "1px solid #e0eae0", borderRadius: 10, padding: "14px 14px 6px", marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#2d4a2d", marginBottom: 6 }}>Margin trend (last 36 months)</div>
            <MarginChart />
          </div>

          {/* year × month matrix */}
          <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: isMobile ? 11 : 13 }}>
              <thead>
                <tr style={{ background: "#f2f6f2" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", color: "#2d4a2d", position: "sticky", left: 0, background: "#f2f6f2" }}>Year</th>
                  {MONTH_ABBR.map((m) => (
                    <th key={m} style={{ padding: "8px 6px", textAlign: "right", color: "#2d4a2d", fontWeight: 600 }}>{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {years.map((yr) => (
                  <tr key={yr} style={{ borderTop: "1px solid #eef3ee" }}>
                    <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1a2e1a", position: "sticky", left: 0, background: "#fff" }}>{yr}</td>
                    {MONTH_ABBR.map((_, mi) => {
                      const r = byYM[`${yr}-${mi + 1}`];
                      if (!r || r.margin == null) return <td key={mi} style={{ padding: "8px 6px", textAlign: "right", color: "#c5d0c5" }}>—</td>;
                      const below = Number(r.margin) < coverage;
                      return (
                        <td
                          key={mi}
                          title={`All-Milk ${fmtD(r.all_milk_price)} − feed ${fmtD(r.feed_cost)} = ${fmtD(r.margin)}`}
                          style={{
                            padding: "8px 6px",
                            textAlign: "right",
                            fontWeight: below ? 700 : 500,
                            color: below ? "#c0392b" : "#1a2e1a",
                            background: below ? "#fdecea" : "transparent",
                          }}
                        >
                          {Number(r.margin).toFixed(2)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: 11, color: "#677167", marginTop: 10 }}>
            Source: USDA FSA — Dairy Margin Coverage Program Updates &amp; Prices. Updated monthly after NASS Agricultural Prices publish (≈1-month lag).
            {latest?.updated_at && ` Last synced ${new Date(latest.updated_at).toLocaleDateString()}.`}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// DRP PERFORMANCE — Basis Calculator
// Basis = NASS Mailbox − Blended DRP Price (60% Class IV + 40% Class III default)
// Forward: today's DRP net floor blended + historical basis = est. mailbox protection
// ─────────────────────────────────────────────────────────────────────────────
function DRPPerformanceView({ isMobile, defaultState = "NY" }) {
  const [milkPrices, setMilkPrices] = useState([]);
  const [drpPrices, setDrpPrices] = useState([]);
  const [classPrices, setClassPrices] = useState([]); // settled AMS Class III/IV monthly (from class_prices table)
  const [dairyByState, setDairyByState] = useState({}); // { NY: [...], MI: [...] } monthly supply rows, cached per state
  const [dairySupplyLoading, setDairySupplyLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState(["NY", "MI", "PA", "MD"].includes((defaultState || "NY").toUpperCase()) ? (defaultState || "NY").toUpperCase() : "NY"); // seeded from farmer's state in portal; falls back to NY
  const [lookbackQuarters, setLookbackQuarters] = useState(6);
  const [classIIIPct, setClassIIIPct] = useState(40);
  const [basisMode, setBasisMode] = useState("trailing"); // "trailing" | "sameQuarter"
  const [viewMode, setViewMode] = useState("supply"); // "basis" | "supply" — Basis Analysis hidden for customer-facing view; Supply Context only

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [milk, drp, cp] = await Promise.all([
          sbFetch("nass_data", {
            select: "state,crop,year,week,stat_type,value",
            filters: ["crop=eq.MILK", "stat_type=eq.PRICE_RECEIVED"]
          }).catch(() => []),
          sbFetch("drp_board", {   // migrated off Ever.Ag → RMA-sourced board
            select: "price_date,quarter,milk_class,price,premium,net_floor"
          }).catch(() => []),
          sbFetch("class_prices", {
            select: "year,month,milk_class,price"
          }).catch(() => []),
        ]);
        setMilkPrices(milk || []);
        setDrpPrices(drp || []);
        setClassPrices(cp || []);
      } catch (e) { console.error("DRP performance load error:", e); }
      setLoading(false);
    }
    load();
  }, []);

  // ── Load dairy supply (production, per-cow, cow inventory, fat test) for the
  // selected state, cached per state. NASS dairy is synced for the states listed
  // in nass-sync.js DAIRY_STATES — NY, MI, PA. States with no rows render an empty
  // note, and MARYLAND is permanently one of them: NASS publishes no true monthly
  // dairy series for MD, only quarterly rollups ("JAN THRU MAR"), which the sync
  // correctly refuses. The empty note is the right answer there, not a bug to fix.
  useEffect(() => {
    if (dairyByState[selectedState]) return; // already loaded this session
    let cancelled = false;
    async function loadSupply() {
      setDairySupplyLoading(true);
      try {
        const rows = await sbFetch("nass_data", {
          select: "state,year,week,stat_type,value,unit,fetched_at",
          filters: [
            "crop=eq.MILK",
            `state=eq.${selectedState}`,
            "stat_type=in.(MILK_PRODUCTION,MILK_PER_COW,MILK_COW_INVENTORY,MILK_FAT_TEST)"
          ],
          order: "year.asc,week.asc",
        }).catch(() => []);
        if (!cancelled) setDairyByState(prev => ({ ...prev, [selectedState]: rows || [] }));
      } catch (e) { console.error("Dairy supply load error:", e); }
      if (!cancelled) setDairySupplyLoading(false);
    }
    loadSupply();
    return () => { cancelled = true; };
  }, [selectedState, dairyByState]);

  const dairySupply = dairyByState[selectedState] || [];

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}>Loading basis data...</div>;

  const classIVPct = 100 - classIIIPct;

  // ── Parse quarter strings ──
  const parseQtr = (qs) => {
    if (!qs) return null;
    // "Q2-25" or "Q1-26"
    let m = qs.match(/Q(\d)[- ](\d{2})$/);
    if (m) return { q: parseInt(m[1]), year: 2000 + parseInt(m[2]) };
    // "Q4 2026"
    m = qs.match(/Q(\d)\s*(\d{4})/);
    if (m) return { q: parseInt(m[1]), year: parseInt(m[2]) };
    // "2026-Q3" or "2027-Q1"
    m = qs.match(/(\d{4})-Q(\d)/);
    if (m) return { q: parseInt(m[2]), year: parseInt(m[1]) };
    return null;
  };
  const qtrKey = (q, yr) => `${yr}-Q${q}`;
  const qtrLabel = (q, yr) => `${yr} Q${q}`;
  const qtrSortVal = (q, yr) => yr * 10 + q;
  const quarterOfMonth = (mm) => Math.ceil(mm / 3);

  // ── Build quarterly NASS mailbox prices ──
  const monthlyPrices = milkPrices.filter(r => r.state === selectedState && r.week != null)
    .sort((a, b) => a.year - b.year || a.week - b.week);
  const quarterlyMailbox = {};
  monthlyPrices.forEach(r => {
    const q = Math.ceil(r.week / 3);
    const key = qtrKey(q, r.year);
    if (!quarterlyMailbox[key]) quarterlyMailbox[key] = { sum: 0, count: 0, year: r.year, quarter: q };
    quarterlyMailbox[key].sum += r.value;
    quarterlyMailbox[key].count += 1;
  });
  const getMailbox = (key) => {
    const d = quarterlyMailbox[key];
    return d && d.count > 0 ? d.sum / d.count : null;
  };

  // ── Build quarterly SETTLED AMS Class III + IV prices ──
  // class_prices has monthly rows. A quarter is only counted when ALL 3 months are present
  // (prevents showing partial-quarter basis values that would be misleading).
  const settledByQtr = {};
  classPrices.forEach(r => {
    const q = quarterOfMonth(r.month);
    const key = qtrKey(q, r.year);
    if (!settledByQtr[key]) settledByQtr[key] = { year: r.year, quarter: q, sumIII: 0, sumIV: 0, monthsIII: 0, monthsIV: 0 };
    const bucket = settledByQtr[key];
    if (r.milk_class === "III") { bucket.sumIII += parseFloat(r.price); bucket.monthsIII += 1; }
    else if (r.milk_class === "IV") { bucket.sumIV += parseFloat(r.price); bucket.monthsIV += 1; }
  });
  const getSettledIII = (key) => {
    const d = settledByQtr[key];
    return d && d.monthsIII === 3 ? d.sumIII / 3 : null;
  };
  const getSettledIV = (key) => {
    const d = settledByQtr[key];
    return d && d.monthsIV === 3 ? d.sumIV / 3 : null;
  };

  // ── Build DRP forward quotes by quarter — latest quote date per class per quarter ──
  const drpByQtr = {};
  drpPrices.forEach(r => {
    const pq = parseQtr(r.quarter);
    if (!pq || !r.net_floor || !r.milk_class) return;
    const key = qtrKey(pq.q, pq.year);
    const isIII = r.milk_class.includes("III");
    const isIV = r.milk_class.includes("IV") && !r.milk_class.includes("III");
    if (!isIII && !isIV) return;
    const cls = isIII ? "III" : "IV";
    const dk = `${key}|${cls}`;
    if (!drpByQtr[dk] || r.price_date > drpByQtr[dk].price_date) {
      drpByQtr[dk] = { key, cls, price_date: r.price_date, net_floor: parseFloat(r.net_floor), quarter: pq.q, year: pq.year };
    }
  });

  // ── Build unified quarter rows ──
  // Past quarters: basis = mailbox - settled class price (3 variants: III, IV, Blended)
  // Future quarters: forward DRP net_floor quotes
  const now = new Date();
  const currentQtr = Math.ceil((now.getMonth() + 1) / 3);
  const currentYear = now.getFullYear();
  const currentSortVal = qtrSortVal(currentQtr, currentYear);

  const allQtrKeys = new Set();
  Object.values(drpByQtr).forEach(d => allQtrKeys.add(d.key));
  Object.keys(quarterlyMailbox).forEach(k => allQtrKeys.add(k));
  Object.keys(settledByQtr).forEach(k => allQtrKeys.add(k));

  const quarterRows = [...allQtrKeys].map(key => {
    const m = key.match(/(\d{4})-Q(\d)/);
    if (!m) return null;
    const year = parseInt(m[1]), quarter = parseInt(m[2]);
    const isPast = qtrSortVal(quarter, year) < currentSortVal;
    const isFuture = qtrSortVal(quarter, year) > currentSortVal;

    const mailbox = getMailbox(key);
    const settledIII = getSettledIII(key);
    const settledIV = getSettledIV(key);
    const settledBlended = settledIII != null && settledIV != null
      ? (settledIII * classIIIPct / 100) + (settledIV * classIVPct / 100) : null;

    // Basis = mailbox − settled class price. Three variants.
    const basisIII = mailbox != null && settledIII != null ? mailbox - settledIII : null;
    const basisIV  = mailbox != null && settledIV  != null ? mailbox - settledIV  : null;
    const basisBlended = mailbox != null && settledBlended != null ? mailbox - settledBlended : null;

    // Forward DRP floors (used in "If you buy DRP today" table)
    const iii = drpByQtr[`${key}|III`];
    const iv = drpByQtr[`${key}|IV`];
    const iiiFloor = iii ? iii.net_floor : null;
    const ivFloor = iv ? iv.net_floor : null;
    const blendedFloor = iiiFloor != null && ivFloor != null
      ? (iiiFloor * classIIIPct / 100) + (ivFloor * classIVPct / 100) : null;

    return {
      key, year, quarter, isPast, isFuture,
      mailbox,
      settledIII, settledIV, settledBlended,
      basisIII, basisIV, basisBlended,
      iiiFloor, ivFloor, blendedFloor,
    };
  }).filter(Boolean).sort((a, b) => qtrSortVal(b.quarter, b.year) - qtrSortVal(a.quarter, a.year));

  // ── Historical basis — three parallel computations (III / IV / Blended) ──
  // A past quarter is included per-route only if it has mailbox AND full settled data for that class.
  const pastIII = quarterRows.filter(q => q.isPast && q.basisIII != null);
  const pastIV = quarterRows.filter(q => q.isPast && q.basisIV != null);
  const pastBlended = quarterRows.filter(q => q.isPast && q.basisBlended != null);

  const maxAvailIII = pastIII.length;
  const maxAvailIV = pastIV.length;
  const maxAvailBlended = pastBlended.length;
  const maxAvailAny = Math.max(maxAvailIII, maxAvailIV, maxAvailBlended);

  const effLookbackIII = Math.min(lookbackQuarters, maxAvailIII);
  const effLookbackIV = Math.min(lookbackQuarters, maxAvailIV);
  const effLookbackBlended = Math.min(lookbackQuarters, maxAvailBlended);

  const basisQuartersIII = pastIII.slice(0, effLookbackIII);
  const basisQuartersIV = pastIV.slice(0, effLookbackIV);
  const basisQuartersBlended = pastBlended.slice(0, effLookbackBlended);

  const avgField = (arr, field) => arr.length > 0 ? arr.reduce((s, q) => s + q[field], 0) / arr.length : null;
  const avgBasisIII = avgField(basisQuartersIII, "basisIII");
  const avgBasisIV = avgField(basisQuartersIV, "basisIV");
  const avgBasisBlended = avgField(basisQuartersBlended, "basisBlended");

  // ── Same-quarter historical basis (Q1/Q2/Q3/Q4 separately) ──
  // Dairy markets are highly seasonal (spring flush vs fall tightness), so same-quarter
  // historical basis is more predictive than a generic trailing window. When basisMode is
  // "sameQuarter", the forward quote table uses these per-quarter-number averages instead.
  const sameQtrBasis = { III: {}, IV: {}, Blended: {} };   // e.g. sameQtrBasis.III[3] = avg basis across all historical Q3s
  const sameQtrCounts = { III: {}, IV: {}, Blended: {} };  // how many historical quarters averaged
  [1, 2, 3, 4].forEach(qn => {
    const iiiSlice = pastIII.filter(q => q.quarter === qn);
    const ivSlice = pastIV.filter(q => q.quarter === qn);
    const blSlice = pastBlended.filter(q => q.quarter === qn);
    sameQtrBasis.III[qn] = avgField(iiiSlice, "basisIII");
    sameQtrBasis.IV[qn] = avgField(ivSlice, "basisIV");
    sameQtrBasis.Blended[qn] = avgField(blSlice, "basisBlended");
    sameQtrCounts.III[qn] = iiiSlice.length;
    sameQtrCounts.IV[qn] = ivSlice.length;
    sameQtrCounts.Blended[qn] = blSlice.length;
  });

  // Basis resolver for forward quote table — returns {basis, count, label} for a given route + quarter number.
  // Respects the current basisMode.
  const resolveForwardBasis = (route, quarterNumber) => {
    if (basisMode === "sameQuarter") {
      const b = sameQtrBasis[route]?.[quarterNumber];
      const c = sameQtrCounts[route]?.[quarterNumber] ?? 0;
      return { basis: b, count: c, label: c > 0 ? `avg of ${c} historical Q${quarterNumber}${c === 1 ? '' : 's'}` : `no historical Q${quarterNumber} data` };
    }
    // trailing
    if (route === "III")     return { basis: avgBasisIII,     count: basisQuartersIII.length,     label: `${basisQuartersIII.length}-qtr trailing avg` };
    if (route === "IV")      return { basis: avgBasisIV,      count: basisQuartersIV.length,      label: `${basisQuartersIV.length}-qtr trailing avg` };
    if (route === "Blended") return { basis: avgBasisBlended, count: basisQuartersBlended.length, label: `${basisQuartersBlended.length}-qtr trailing avg` };
    return { basis: null, count: 0, label: "" };
  };

  // Build lookback options: only show values up to available data
  const lookbackOptions = [2, 4, 6, 8, 12].filter(n => n <= maxAvailAny);
  if (lookbackOptions.length === 0 && maxAvailAny > 0) lookbackOptions.push(maxAvailAny);
  if (lookbackOptions.length > 0 && !lookbackOptions.includes(maxAvailAny) && maxAvailAny > 0) {
    lookbackOptions.push(maxAvailAny);
    lookbackOptions.sort((a, b) => a - b);
  }

  // Forward quotes displayed chronologically (nearest quarter at top, furthest at bottom).
  // quarterRows is sorted newest-first for the historical table, so we reverse here.
  const futureQuarters = quarterRows.filter(q => q.isFuture && q.blendedFloor != null)
    .slice().reverse();

  // ── Annual mailbox — use NASS annual where available, calculate from monthly where missing ──
  const nassAnnual = milkPrices.filter(r => r.state === selectedState && r.week == null);
  const nassAnnualYears = new Set(nassAnnual.map(r => r.year));
  const monthlyByYear = {};
  monthlyPrices.forEach(r => {
    if (!monthlyByYear[r.year]) monthlyByYear[r.year] = { sum: 0, count: 0 };
    monthlyByYear[r.year].sum += r.value;
    monthlyByYear[r.year].count += 1;
  });
  const annualPrices = [];
  nassAnnual.forEach(r => annualPrices.push({ year: r.year, value: r.value, source: 'nass' }));
  Object.entries(monthlyByYear).forEach(([yr, d]) => {
    const y = parseInt(yr);
    if (!nassAnnualYears.has(y) && d.count >= 12) {
      annualPrices.push({ year: y, value: Math.round((d.sum / d.count) * 100) / 100, source: 'calculated' });
    }
  });
  annualPrices.sort((a, b) => b.year - a.year);

  const fmtPrice = (v) => v != null ? `$${v.toFixed(2)}` : '\u2014';
  const fmtBasis = (v) => {
    if (v == null) return '\u2014';
    return `${v >= 0 ? '+' : ''}$${v.toFixed(2)}`;
  };
  const states = ["NY", "PA", "OH", "IN", "MD", "MI"];

  // ── Basis summary card renderer (reused for III / IV / Blended) ──
  const BasisSummaryCard = ({ label, avgBasis, basisQuarters, fieldName, accentColor = "#4a6e4a", primary = false }) => {
    const values = basisQuarters.map(q => q[fieldName]).filter(v => v != null);
    return (
      <div style={{
        background: primary ? "#fff" : "#f4f8f4",
        border: primary ? `2px solid ${accentColor}` : "1px solid #d0ddd0",
        borderRadius: 10,
        padding: 16,
      }}>
        <div style={{ fontSize: 10, color: "#60735f", textTransform: "uppercase", fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>
          {label}
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: avgBasis != null && avgBasis >= 0 ? "#059669" : (avgBasis != null ? "#DC2626" : "#999"), fontVariantNumeric: "tabular-nums" }}>
          {avgBasis != null ? fmtBasis(avgBasis) : '—'}
        </div>
        <div style={{ fontSize: 10, color: "#999", marginTop: 4 }}>
          {basisQuarters.length > 0 ? `${basisQuarters.length} qtrs trailing` : 'insufficient data'}
        </div>
        {values.length > 0 && (
          <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>
            range {fmtBasis(Math.min(...values))} to {fmtBasis(Math.max(...values))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? "16px 0" : "20px 0" }}>
      {/* Basis Analysis is intentionally hidden from this view (customer-facing).
          viewMode stays "supply"; the basis block below is retained but never
          rendered. To restore the toggle, default viewMode to "basis" and add
          back the two-button switcher. */}

      {viewMode === "basis" && <>
      {/* Controls */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#60735f", fontWeight: 600 }}>State:</span>
          <div style={{ display: "flex", gap: 3 }}>
            {states.map(st => (
              <button key={st} onClick={() => setSelectedState(st)}
                style={{ padding: "4px 8px", fontSize: 11, fontWeight: selectedState === st ? 700 : 400,
                  background: selectedState === st ? "#4a6e4a" : "#f4f8f4",
                  color: selectedState === st ? "#fff" : "#4a6e4a",
                  border: "1px solid #d0ddd0", borderRadius: 4, cursor: "pointer" }}>{st}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#60735f", fontWeight: 600 }}>Basis lookback:</span>
          <select value={lookbackQuarters} onChange={e => setLookbackQuarters(parseInt(e.target.value))}
            style={{ padding: "4px 8px", fontSize: 12, border: "1px solid #d0ddd0", borderRadius: 4, background: "#fff" }}>
            {lookbackOptions.length > 0
              ? lookbackOptions.map(n => <option key={n} value={n}>{n} quarters</option>)
              : <option value={0}>No basis data</option>}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#60735f", fontWeight: 600 }}>Blend split:</span>
          <select value={classIIIPct} onChange={e => setClassIIIPct(parseInt(e.target.value))}
            style={{ padding: "4px 8px", fontSize: 12, border: "1px solid #d0ddd0", borderRadius: 4, background: "#fff" }}>
            <option value={40}>40% III / 60% IV</option>
            <option value={50}>50% III / 50% IV</option>
            <option value={60}>60% III / 40% IV</option>
            <option value={70}>70% III / 30% IV</option>
            <option value={100}>100% III</option>
            <option value={0}>100% IV</option>
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#60735f", fontWeight: 600 }}>Forward basis method:</span>
          <div style={{ display: "flex", border: "1px solid #d0ddd0", borderRadius: 4, overflow: "hidden" }}>
            {[
              { key: "trailing", label: "Trailing" },
              { key: "sameQuarter", label: "Same quarter" },
            ].map(opt => {
              const active = basisMode === opt.key;
              return (
                <button key={opt.key} onClick={() => setBasisMode(opt.key)}
                  style={{ padding: "4px 10px", fontSize: 11, fontWeight: active ? 700 : 500,
                    background: active ? "#4a6e4a" : "#fff",
                    color: active ? "#fff" : "#4a6e4a",
                    border: "none", cursor: "pointer" }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Basis summary — THREE CARDS (III / IV / Blended) */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a", marginBottom: 10, fontFamily: "'Lora','Georgia',serif" }}>
          Historical Basis — {selectedState} vs Settled USDA Class Prices
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
          <BasisSummaryCard label="Mailbox − Class III" avgBasis={avgBasisIII} basisQuarters={basisQuartersIII} fieldName="basisIII" accentColor="#4a6e4a" />
          <BasisSummaryCard label="Mailbox − Class IV" avgBasis={avgBasisIV} basisQuarters={basisQuartersIV} fieldName="basisIV" accentColor="#6b9bd2" />
          <BasisSummaryCard label={`Mailbox − Blended (${classIIIPct}/${classIVPct})`} avgBasis={avgBasisBlended} basisQuarters={basisQuartersBlended} fieldName="basisBlended" accentColor="#4a6e4a" primary />
        </div>
      </div>

      {/* Forward quote — estimated mailbox protection, three routes per quarter */}
      {futureQuarters.length > 0 && (() => {
        // Latest DRP quote date for subtitle
        const latestDRPDate = (() => {
          let latest = null;
          for (const r of drpPrices) {
            if (r.price_date && (!latest || r.price_date > latest)) latest = r.price_date;
          }
          if (!latest) return null;
          const dt = new Date(latest + "T12:00:00");
          return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        })();
        // Compact styling on mobile for screenshot-friendly layout
        const thPad = isMobile ? "6px 6px" : "10px 12px";
        const tdPad = isMobile ? "6px 6px" : "8px 12px";
        const qtrPad = isMobile ? "6px 6px" : "10px 12px";
        const tblFont = isMobile ? 11 : 13;
        const routeFont = isMobile ? 10 : 12;
        const estFontBold = isMobile ? 12 : 14;
        const estFontNormal = isMobile ? 11 : 13;
        return (
        <div style={{ background: "#fff", border: "2px solid #4a6e4a", borderRadius: 10, padding: isMobile ? 12 : 20, marginBottom: 24 }}>
          <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: "#1a2e1a", marginBottom: 2, fontFamily: "'Lora','Georgia',serif" }}>
            If You Buy DRP Today — Estimated Mailbox Protection
          </div>
          {latestDRPDate && (
            <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 6 }}>
              Based on DRP quotes from {latestDRPDate}
            </div>
          )}
          <div style={{ fontSize: isMobile ? 10 : 11, color: "#60735f", marginBottom: isMobile ? 10 : 14 }}>
            DRP net floor (after premium) + historical basis by route.{' '}
            {basisMode === "sameQuarter"
              ? <span><strong>Same-quarter mode:</strong> basis uses historical Q1/Q2/Q3/Q4 averages across all years — captures dairy seasonality.</span>
              : <span><strong>Trailing mode:</strong> basis uses the {basisQuartersBlended.length}-quarter trailing average.</span>
            }
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: tblFont, tableLayout: isMobile ? "fixed" : "auto" }}>
              <thead>
                <tr style={{ background: "#f4f8f4" }}>
                  <th style={{ textAlign: "left", padding: thPad, fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Quarter</th>
                  <th style={{ textAlign: "right", padding: thPad, fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Route</th>
                  <th style={{ textAlign: "right", padding: thPad, fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>{isMobile ? "Net Floor" : "DRP Net Floor"}</th>
                  <th style={{ textAlign: "right", padding: thPad, fontWeight: 700, color: "#059669", borderBottom: "1px solid #e0eae0" }}>{isMobile ? "+ Basis" : "+ Avg Basis"}</th>
                  <th style={{ textAlign: "right", padding: thPad, fontWeight: 700, color: "#1a2e1a", fontSize: isMobile ? 11 : 14, borderBottom: "1px solid #e0eae0" }}>{isMobile ? "Est. Mailbox" : "Est. Mailbox Floor"}</th>
                </tr>
              </thead>
              <tbody>
                {futureQuarters.map(q => {
                  const basisIIIInfo     = resolveForwardBasis("III",     q.quarter);
                  const basisIVInfo      = resolveForwardBasis("IV",      q.quarter);
                  const basisBlendedInfo = resolveForwardBasis("Blended", q.quarter);
                  const routes = [
                    { label: "Class III", floor: q.iiiFloor,     basisInfo: basisIIIInfo,     tint: "#4a6e4a", bold: false },
                    { label: "Class IV",  floor: q.ivFloor,      basisInfo: basisIVInfo,      tint: "#6b9bd2", bold: false },
                    { label: `Blended ${classIIIPct}/${classIVPct}`, floor: q.blendedFloor, basisInfo: basisBlendedInfo, tint: "#1a2e1a", bold: true },
                  ];
                  return routes.map((rt, i) => {
                    const b = rt.basisInfo.basis;
                    const estMailbox = rt.floor != null && b != null ? rt.floor + b : null;
                    return (
                      <tr key={`${q.key}-${rt.label}`} style={{
                        borderBottom: i === 2 ? "1px solid #d0ddd0" : "1px solid #f6f8f6",
                        background: rt.bold ? "#fafcfa" : "#fff",
                      }}>
                        {i === 0 && <td rowSpan={3} style={{ padding: qtrPad, fontWeight: 700, color: "#1a2e1a", verticalAlign: "top", borderRight: "1px solid #f0f4f0", fontSize: isMobile ? 11 : 13 }}>{qtrLabel(q.quarter, q.year)}</td>}
                        <td style={{ padding: tdPad, textAlign: "right", color: rt.tint, fontWeight: rt.bold ? 700 : 500, fontSize: routeFont }}>{rt.label}</td>
                        <td style={{ padding: tdPad, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#666" }}>{fmtPrice(rt.floor)}</td>
                        <td style={{ padding: tdPad, textAlign: "right", fontVariantNumeric: "tabular-nums", color: b != null ? "#059669" : "#ccc" }}>
                          {fmtBasis(b)}
                          {basisMode === "sameQuarter" && rt.basisInfo.count > 0 && (
                            <div style={{ fontSize: 10, color: "#999", fontWeight: 400, marginTop: 2 }}>
                              {rt.basisInfo.count} yr{rt.basisInfo.count === 1 ? '' : 's'} of Q{q.quarter}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: tdPad, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: rt.bold ? 800 : 600, fontSize: rt.bold ? estFontBold : estFontNormal, color: "#1a2e1a" }}>{fmtPrice(estMailbox)}</td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

      {/* Historical basis detail — three-route per quarter */}
      <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #e0eae0", fontSize: 14, fontWeight: 700, color: "#1a2e1a", fontFamily: "'Lora','Georgia',serif" }}>
          Historical Basis by Quarter (Mailbox − Settled Class Price)
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f4f8f4" }}>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Quarter</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#6b9bd2", borderBottom: "1px solid #e0eae0" }}>Mailbox</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Class III</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Class IV</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#4a6e4a", borderBottom: "1px solid #e0eae0" }}>Blended</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#059669", borderBottom: "1px solid #e0eae0" }}>Basis III</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#059669", borderBottom: "1px solid #e0eae0" }}>Basis IV</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#059669", borderBottom: "1px solid #e0eae0" }}>Basis Blended</th>
              </tr>
            </thead>
            <tbody>
              {quarterRows.filter(q => q.isPast && (q.basisIII != null || q.basisIV != null || q.basisBlended != null)).map(q => {
                const inWindowIII = basisQuartersIII.some(bq => bq.key === q.key);
                const inWindowIV = basisQuartersIV.some(bq => bq.key === q.key);
                const inWindowBlended = basisQuartersBlended.some(bq => bq.key === q.key);
                const anyInWindow = inWindowIII || inWindowIV || inWindowBlended;
                return (
                  <tr key={q.key} style={{ borderBottom: "1px solid #f0f4f0", background: anyInWindow ? "#f8faf8" : "transparent", opacity: anyInWindow ? 1 : 0.5 }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: "#1a2e1a" }}>
                      {qtrLabel(q.quarter, q.year)}{anyInWindow && <span style={{ fontSize: 10, color: "#4a6e4a", marginLeft: 4 }}>●</span>}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#6b9bd2" }}>{fmtPrice(q.mailbox)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#666" }}>{fmtPrice(q.settledIII)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#666" }}>{fmtPrice(q.settledIV)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "#4a6e4a" }}>{fmtPrice(q.settledBlended)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700,
                      color: q.basisIII != null ? (q.basisIII >= 0 ? "#059669" : "#DC2626") : "#ccc" }}>{fmtBasis(q.basisIII)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700,
                      color: q.basisIV != null ? (q.basisIV >= 0 ? "#059669" : "#DC2626") : "#ccc" }}>{fmtBasis(q.basisIV)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700,
                      color: q.basisBlended != null ? (q.basisBlended >= 0 ? "#059669" : "#DC2626") : "#ccc" }}>{fmtBasis(q.basisBlended)}</td>
                  </tr>
                );
              })}
              {maxAvailAny === 0 && (
                <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: "#999" }}>
                  No historical quarters with both mailbox and settled class price data yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Historical mailbox prices */}
      <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #e0eae0", fontSize: 14, fontWeight: 700, color: "#1a2e1a", fontFamily: "'Lora','Georgia',serif" }}>
          Historical Mailbox Prices — {selectedState} (NASS)
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f4f8f4" }}>
                <th style={{ textAlign: "left", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Quarter</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Avg Mailbox ($/cwt)</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#60735f", borderBottom: "1px solid #e0eae0" }}>Months</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(quarterlyMailbox)
                .map(q => ({ ...q, avg: q.count > 0 ? q.sum / q.count : null }))
                .sort((a, b) => b.year - a.year || b.quarter - a.quarter)
                .map(q => (
                  <tr key={qtrKey(q.quarter, q.year)} style={{ borderBottom: "1px solid #f0f4f0" }}>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1a2e1a" }}>{qtrLabel(q.quarter, q.year)}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#4a6e4a", fontSize: 14 }}>{fmtPrice(q.avg)}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#999" }}>{q.count}/3</td>
                  </tr>
                ))
              }
              {Object.keys(quarterlyMailbox).length === 0 && (
                <tr><td colSpan={3} style={{ padding: 20, textAlign: "center", color: "#999" }}>
                  No milk price data. Run NASS sync with prices dataset.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Annual mailbox */}
      {annualPrices.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, overflow: "hidden", marginBottom: 24 }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e0eae0", fontSize: 14, fontWeight: 700, color: "#1a2e1a", fontFamily: "'Lora','Georgia',serif" }}>
            Annual Avg Mailbox Price — {selectedState}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f4f8f4" }}>
                <th style={{ textAlign: "left", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Year</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Price Received ($/cwt)</th>
              </tr>
            </thead>
            <tbody>
              {annualPrices.map(r => (
                <tr key={r.year} style={{ borderBottom: "1px solid #f0f4f0" }}>
                  <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1a2e1a" }}>{r.year}{r.source === 'calculated' ? <span style={{ fontSize: 10, color: "#999", fontWeight: 400, marginLeft: 4 }}>(avg of monthly)</span> : ''}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#4a6e4a", fontSize: 14 }}>{fmtPrice(r.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Explanation */}
      <div style={{ padding: 16, background: "#f8faf8", border: "1px solid #e0eae0", borderRadius: 8, fontSize: 12, color: "#4a6e4a", lineHeight: 1.6 }}>
        <strong>How this works:</strong> Historical basis = NASS mailbox price − settled USDA AMS Class III/IV monthly announced prices (aggregated to quarter averages).
        We show three routes side-by-side so you can pick the one matching how your co-op prices milk:
        <strong> Class III</strong> (cheese-priced co-ops),
        <strong> Class IV</strong> (butter/powder co-ops),
        or a <strong>blended {classIIIPct}/{classIVPct}</strong> formula.
        Estimated forward mailbox = DRP net floor (after premium) + trailing {lookbackQuarters}-quarter avg basis for the chosen route.
      </div>
      </>}

      {viewMode === "supply" && <DRPSupplyContext
        dairySupply={dairySupply}
        dairySupplyLoading={dairySupplyLoading}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        isMobile={isMobile}
      />}
    </div>
  );
}

// ── Federal Order milk-marketing card ─────────────────────────
// Surfaces the selected state's FMMO Statistical Uniform Price (blend price) and
// Producer Price Differential from the `ppd` table (keyed by order_no). NY = Order
// 001 (Northeast); MI = Order 033 (Mideast). These are the producer-facing minimum
// pool prices a sharp dairy tracks. Component prices (butterfat/protein $/lb) are
// announced nationally and identical across orders, so they're noted, not per-state.
function FmmoMarketingCard({ selectedState, isMobile }) {
  const fmmo = STATE_FMMO_ORDER[selectedState] || null;
  const [rows, setRows] = useState(null); // null = loading, [] = none

  useEffect(() => {
    if (!fmmo) { setRows([]); return; }
    let cancelled = false;
    (async () => {
      setRows(null);
      const r = await sbFetch("ppd", {
        select: "year,month,order_no,order_name,sup,ppd,fetched_at",
        filters: [`order_no=eq.${fmmo.order}`],
        order: "year.desc,month.desc",
      }).catch(() => []);
      if (!cancelled) setRows(r || []);
    })();
    return () => { cancelled = true; };
  }, [selectedState]);

  if (!fmmo) return null;

  const MON = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const f2 = (v) => (v == null || isNaN(Number(v))) ? "—" : `$${Number(v).toFixed(2)}`;
  const valid = (rows || []).filter(r => r.sup != null || r.ppd != null);
  const latest = valid[0] || null;
  const recent = valid.slice(0, 6);
  // Trailing-12-month average uniform price (a steadier read than the latest month).
  const last12 = valid.slice(0, 12).map(r => Number(r.sup)).filter(v => !isNaN(v));
  const avgSup = last12.length ? last12.reduce((a, b) => a + b, 0) / last12.length : null;

  const card = { background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: 16, flex: 1, minWidth: 0 };
  const kLabel = { fontSize: 10, color: "#60735f", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 };
  const kVal = { fontSize: 24, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums", marginBottom: 4 };

  return (
    <div style={{ background: "#f8faf8", border: "1px solid #e0eae0", borderRadius: 10, padding: 16, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a", fontFamily: "'Lora','Georgia',serif" }}>
          Federal Order Milk Marketing — Order {fmmo.order} ({fmmo.name})
        </div>
        <FreshAsOf date={latestTs(valid, "fetched_at")} label="FMMO pool" prefix="synced" />
      </div>
      <div style={{ fontSize: 12, color: "#60735f", marginBottom: 14, lineHeight: 1.5 }}>
        The producer-facing pool prices for a {selectedState} dairy. The <strong>Statistical Uniform Price</strong> is the
        minimum blend price (at standard test) the order pays; the <strong>PPD</strong> is the premium over the Class III price.
      </div>
      {rows === null ? (
        <div style={{ color: "#60735f", fontSize: 13, padding: "8px 0" }}>Loading Order {fmmo.order} prices…</div>
      ) : !latest ? (
        <div style={{ color: "#60735f", fontSize: 13, padding: "8px 0" }}>No Order {fmmo.order} pool-price data found.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <div style={card}>
              <div style={kLabel}>Statistical Uniform Price</div>
              <div style={kVal}>{f2(latest.sup)}</div>
              <div style={{ fontSize: 11, color: "#60735f" }}>{MON[latest.month]} {latest.year} · $/cwt blend</div>
            </div>
            <div style={card}>
              <div style={kLabel}>Producer Price Differential</div>
              <div style={kVal}>{f2(latest.ppd)}</div>
              <div style={{ fontSize: 11, color: "#60735f" }}>{MON[latest.month]} {latest.year} · over Class III</div>
            </div>
            <div style={card}>
              <div style={kLabel}>12-mo Avg Uniform Price</div>
              <div style={kVal}>{avgSup == null ? "—" : `$${avgSup.toFixed(2)}`}</div>
              <div style={{ fontSize: 11, color: "#60735f" }}>trailing 12 months · $/cwt</div>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, background: "#fff", borderRadius: 8, overflow: "hidden" }}>
              <thead>
                <tr style={{ background: "#f4f8f4" }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Month</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Uniform Price ($/cwt)</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#4a6e4a", borderBottom: "1px solid #e0eae0" }}>PPD ($/cwt)</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(r => (
                  <tr key={`${r.year}-${r.month}`} style={{ borderBottom: "1px solid #f0f4f0" }}>
                    <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1a2e1a" }}>{MON[r.month]} {r.year}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#4a6e4a" }}>{f2(r.sup)}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: Number(r.ppd) < 0 ? "#DC2626" : "#1a2e1a" }}>{f2(r.ppd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: "#677167", marginTop: 10, lineHeight: 1.5 }}>
            Butterfat, protein, and other-solids component prices ($/lb) are announced nationally and are identical across all
            Federal Orders — only the uniform price and PPD differ by order. Source: USDA AMS pool statistics.
          </div>
        </>
      )}
    </div>
  );
}

// ── Supply Context sub-view ───────────────────────────────────
// Renders dairy supply KPIs, current vs prior year overlay chart, and monthly
// history table for the selected state, plus the FMMO milk-marketing card.
// Data comes from nass_data where stat_type in (MILK_PRODUCTION, MILK_PER_COW, MILK_COW_INVENTORY, MILK_FAT_TEST).
function DRPSupplyContext({ dairySupply, dairySupplyLoading, selectedState, setSelectedState, isMobile }) {
  const fmmo = STATE_FMMO_ORDER[selectedState] || null;
  const SUPPLY_STATES = ["NY", "MI", "PA", "MD"];
  const stateHeader = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#60735f", textTransform: "uppercase", letterSpacing: 0.5 }}>State</span>
      {SUPPLY_STATES.map(st => (
        <button key={st} onClick={() => setSelectedState(st)}
          style={{ padding: "6px 14px", borderRadius: 8, border: selectedState === st ? "2px solid #4a6e4a" : "1px solid #d0e0d0",
            background: selectedState === st ? "#4a6e4a" : "#fff", color: selectedState === st ? "#fff" : "#4a6e4a",
            fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
          {st}
        </button>
      ))}
      {fmmo && <span style={{ fontSize: 11, color: "#677167", marginLeft: 4 }}>FMMO {fmmo.order} · {fmmo.name}</span>}
      <FreshAsOf date={latestTs(dairySupply, "fetched_at")} label="NASS supply" prefix="synced" style={{ marginLeft: "auto" }} />
    </div>
  );

  if (dairySupplyLoading) {
    return (
      <div>
        {stateHeader}
        <FmmoMarketingCard selectedState={selectedState} isMobile={isMobile} />
        <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}>Loading {selectedState} dairy supply…</div>
      </div>
    );
  }

  if (!dairySupply || dairySupply.length === 0) {
    return (
      <div>
        {stateHeader}
        <FmmoMarketingCard selectedState={selectedState} isMobile={isMobile} />
        <div style={{ padding: 40, textAlign: "center", background: "#f8faf8", border: "1px solid #e0eae0", borderRadius: 10, color: "#4a6e4a" }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, fontFamily: "'Lora','Georgia',serif" }}>No monthly milk supply for {selectedState}</div>
          <div style={{ fontSize: 13, color: "#60735f", lineHeight: 1.5 }}>
            NASS publishes monthly milk production, per-cow yield, cow inventory, and butterfat only for its 24 major dairy states.
            {selectedState} isn't one of them, so there's no monthly supply series to chart — the Federal Order marketing context
            above is the available state-specific data.
          </div>
        </div>
      </div>
    );
  }

  // ── Organize rows by stat_type → { year, month } → value ──
  const byStat = { MILK_PRODUCTION: {}, MILK_PER_COW: {}, MILK_COW_INVENTORY: {}, MILK_FAT_TEST: {} };
  for (const r of dairySupply) {
    if (!r.stat_type || !byStat[r.stat_type]) continue;
    if (r.year == null || r.week == null) continue;
    const key = `${r.year}-${r.week}`;
    byStat[r.stat_type][key] = { year: r.year, month: r.week, value: Number(r.value) };
  }

  // ── Latest + YoY for each series ──
  const latestAndYoY = (stat) => {
    const points = Object.values(byStat[stat]);
    if (points.length === 0) return { latest: null, prior: null, yoyPct: null, label: null };
    points.sort((a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month));
    const latest = points[points.length - 1];
    const prior = points.find(p => p.year === latest.year - 1 && p.month === latest.month);
    const yoyPct = (prior && prior.value) ? ((latest.value - prior.value) / prior.value) * 100 : null;
    const MON = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return { latest, prior, yoyPct, label: `${MON[latest.month]} ${latest.year}` };
  };

  const production = latestAndYoY("MILK_PRODUCTION");
  const perCow = latestAndYoY("MILK_PER_COW");
  const inventory = latestAndYoY("MILK_COW_INVENTORY");
  const fatTest = latestAndYoY("MILK_FAT_TEST");

  // ── Format helpers ──
  const fmtLb = (v) => {
    if (v == null) return "—";
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B lb`;   // NY monthly production ~1.4B
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M lb`;   // other smaller aggregates
    if (v >= 1000) return `${(v / 1000).toFixed(0)}K lb`; // per-cow in thousands wouldn't hit this but safe
    return `${v.toFixed(0)} lb`;
  };
  const fmtHead = (v) => v == null ? "—" : `${(v / 1000).toFixed(0)}K`;
  const fmtLbPerCow = (v) => v == null ? "—" : `${v.toLocaleString()} lb`;
  const fmtPct = (v) => v == null ? "—" : `${v.toFixed(2)}%`;
  const fmtYoy = (p) => p == null ? "—" : `${p >= 0 ? "+" : ""}${p.toFixed(1)}%`;
  const yoyColor = (p) => p == null ? "#999" : (p > 0 ? "#059669" : p < 0 ? "#DC2626" : "#60735f");

  // ── KPI card ──
  const KPI = ({ title, latest, yoy, label, fmtFn, subtitle }) => (
    <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: 18 }}>
      <div style={{ fontSize: 10, color: "#60735f", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums", marginBottom: 4 }}>
        {latest ? fmtFn(latest.value) : "—"}
      </div>
      <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8 }}>{label || ""}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: yoyColor(yoy), fontVariantNumeric: "tabular-nums" }}>{fmtYoy(yoy)}</span>
        <span style={{ fontSize: 10, color: "#999" }}>YoY · {subtitle}</span>
      </div>
    </div>
  );

  // ── Build chart data: current year + prior year monthly MILK_PRODUCTION ──
  // We chart production since it's the cleanest basis/supply indicator.
  // ── Find the latest data year (used by history table and downstream components) ──
  const prodPoints = Object.values(byStat.MILK_PRODUCTION).sort((a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month));

  // ── Monthly history table (most-recent 24 months with YoY deltas) ──
  const historyRows = prodPoints.slice(-24).reverse().map(p => {
    const prior = byStat.MILK_PRODUCTION[`${p.year - 1}-${p.month}`];
    const prodYoy = (prior && prior.value) ? ((p.value - prior.value) / prior.value) * 100 : null;
    const invRow = byStat.MILK_COW_INVENTORY[`${p.year}-${p.month}`];
    const invPrior = byStat.MILK_COW_INVENTORY[`${p.year - 1}-${p.month}`];
    const invYoy = (invRow && invPrior && invPrior.value) ? ((invRow.value - invPrior.value) / invPrior.value) * 100 : null;
    const perCowRow = byStat.MILK_PER_COW[`${p.year}-${p.month}`];
    const perCowPrior = byStat.MILK_PER_COW[`${p.year - 1}-${p.month}`];
    const perCowYoy = (perCowRow && perCowPrior && perCowPrior.value) ? ((perCowRow.value - perCowPrior.value) / perCowPrior.value) * 100 : null;
    const fatRow = byStat.MILK_FAT_TEST[`${p.year}-${p.month}`];
    const fatPrior = byStat.MILK_FAT_TEST[`${p.year - 1}-${p.month}`];
    // Butterfat YoY is reported in absolute percentage-point change (a richer signal
    // than % of % for a narrow-range ratio like fat test).
    const fatYoy = (fatRow && fatPrior) ? (fatRow.value - fatPrior.value) : null;
    const MON = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return {
      key: `${p.year}-${p.month}`,
      label: `${MON[p.month]} ${p.year}`,
      prod: p.value, prodYoy,
      inv: invRow ? invRow.value : null, invYoy,
      perCow: perCowRow ? perCowRow.value : null, perCowYoy,
      fat: fatRow ? fatRow.value : null, fatYoy,
    };
  });

  return (
    <div>
      {stateHeader}
      <FmmoMarketingCard selectedState={selectedState} isMobile={isMobile} />

      {/* Header note */}
      <div style={{ fontSize: 12, color: "#60735f", marginBottom: 16, lineHeight: 1.5 }}>
        {selectedState} dairy supply context for DRP basis analysis. When production rises faster than seasonal, expect Class III basis to soften.
        When cow numbers contract, supply discipline often supports stronger basis in following quarters.
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
        <KPI title="Milk Production" latest={production.latest} yoy={production.yoyPct} label={production.label}
          fmtFn={fmtLb} subtitle="supply driver" />
        <KPI title="Milk Cow Inventory" latest={inventory.latest} yoy={inventory.yoyPct} label={inventory.label}
          fmtFn={fmtHead} subtitle="herd size" />
        <KPI title="Production per Cow" latest={perCow.latest} yoy={perCow.yoyPct} label={perCow.label}
          fmtFn={fmtLbPerCow} subtitle="productivity" />
        {/* Butterfat uses a specialized renderer: abs pp change, not YoY % */}
        <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: 18 }}>
          <div style={{ fontSize: 10, color: "#60735f", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>Butterfat Test</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums", marginBottom: 4 }}>
            {fatTest.latest ? fmtPct(fatTest.latest.value) : "—"}
          </div>
          <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8 }}>{fatTest.label || ""}</div>
          {(() => {
            // Recompute butterfat YoY as absolute pp change for display
            const latest = fatTest.latest, prior = fatTest.prior;
            const ppChange = (latest && prior) ? (latest.value - prior.value) : null;
            const color = ppChange == null ? "#999" : (ppChange > 0 ? "#059669" : ppChange < 0 ? "#DC2626" : "#60735f");
            const label = ppChange == null ? "—" : `${ppChange >= 0 ? "+" : ""}${ppChange.toFixed(2)} pp`;
            return (
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{label}</span>
                <span style={{ fontSize: 10, color: "#999" }}>YoY · mailbox richness</span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Interactive dairy chart — spaghetti mode, 4 metrics, click/hover/zoom */}
      <DairySpaghettiChart byStat={byStat} selectedState={selectedState} isMobile={isMobile} fmtLb={fmtLb} fmtHead={fmtHead} fmtLbPerCow={fmtLbPerCow} fmtPct={fmtPct} />

      {/* Monthly history table */}
      <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #e0eae0", fontSize: 14, fontWeight: 700, color: "#1a2e1a", fontFamily: "'Lora','Georgia',serif" }}>
          {selectedState} Monthly Supply — trailing 24 months
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f4f8f4" }}>
                <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Month</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Production</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#4a6e4a", borderBottom: "1px solid #e0eae0" }}>YoY</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Cows</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#4a6e4a", borderBottom: "1px solid #e0eae0" }}>YoY</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Lb/Cow</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#4a6e4a", borderBottom: "1px solid #e0eae0" }}>YoY</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Fat %</th>
                <th style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: "#4a6e4a", borderBottom: "1px solid #e0eae0" }}>Δ pp</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.map(r => {
                const fatColor = r.fatYoy == null ? "#999" : (r.fatYoy > 0 ? "#059669" : r.fatYoy < 0 ? "#DC2626" : "#60735f");
                const fatLabel = r.fatYoy == null ? "—" : `${r.fatYoy >= 0 ? "+" : ""}${r.fatYoy.toFixed(2)}`;
                return (
                <tr key={r.key} style={{ borderBottom: "1px solid #f0f4f0" }}>
                  <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1a2e1a" }}>{r.label}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>{fmtLb(r.prod)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: yoyColor(r.prodYoy) }}>{fmtYoy(r.prodYoy)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>{r.inv != null ? fmtHead(r.inv) : "—"}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: yoyColor(r.invYoy) }}>{fmtYoy(r.invYoy)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>{r.perCow != null ? fmtLbPerCow(r.perCow) : "—"}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: yoyColor(r.perCowYoy) }}>{fmtYoy(r.perCowYoy)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>{r.fat != null ? fmtPct(r.fat) : "—"}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: fatColor }}>{fatLabel}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Explanation */}
      <div style={{ padding: 16, background: "#f8faf8", border: "1px solid #e0eae0", borderRadius: 8, fontSize: 12, color: "#4a6e4a", lineHeight: 1.6 }}>
        <strong>How to read this:</strong> {selectedState} supply context complements the basis analysis on the prior tab.
        Rising production with flat/declining cow numbers means productivity gains — historically neutral for basis.
        Rising production <em>with</em> rising cow numbers signals real supply expansion — bearish for basis in following quarters.
        Contracting herd size has often preceded basis strength within 2-3 quarters.
        Butterfat is reported at standard fat basis in Class III/IV prices but the mailbox reflects actual fat test —
        rising butterfat adds to mailbox value at the same Class price.
      </div>
    </div>
  );
}

// ── Interactive spaghetti chart: Jan-Dec x-axis, one line per selected year ──
// Features: 4-metric picker, year chips (on/off), hover crosshair + tooltip,
// click-to-pin + delta-to-second-click, brush-drag horizontal zoom, legend click-to-toggle.
// Data in: byStat = { MILK_PRODUCTION: { "2025-1": {year,month,value}, ... }, ... }
function DairySpaghettiChart({ byStat, selectedState = "NY", isMobile, fmtLb, fmtHead, fmtLbPerCow, fmtPct }) {
  const METRICS = [
    { key: "MILK_PRODUCTION",    label: "Production",   fmt: fmtLb,        yUnit: "lb",      yAxisFmt: fmtLb },
    { key: "MILK_COW_INVENTORY", label: "Cow Inventory", fmt: fmtHead,      yUnit: "head",    yAxisFmt: fmtHead },
    { key: "MILK_PER_COW",       label: "Lb / Cow",      fmt: fmtLbPerCow,  yUnit: "lb/cow",  yAxisFmt: fmtLbPerCow },
    { key: "MILK_FAT_TEST",      label: "Butterfat",     fmt: fmtPct,       yUnit: "%",       yAxisFmt: fmtPct },
  ];

  // ── Discover all years that have data for ANY metric ──
  const allYears = useMemo(() => {
    const s = new Set();
    for (const stat of Object.keys(byStat)) {
      for (const k of Object.keys(byStat[stat])) {
        const y = byStat[stat][k]?.year;
        if (y) s.add(y);
      }
    }
    return Array.from(s).sort((a, b) => a - b);
  }, [byStat]);

  const latestYear = allYears.length ? allYears[allYears.length - 1] : null;

  // ── State ──
  const [metricKey, setMetricKey] = useState("MILK_PRODUCTION");
  // Default visible years = current + prior
  const [visibleYears, setVisibleYears] = useState(
    () => latestYear ? new Set([latestYear, latestYear - 1]) : new Set()
  );
  // Dimmed (loaded but 2nd-tier): prior 3 years before the current/prior pair
  const [dimmedYears, setDimmedYears] = useState(
    () => latestYear ? new Set([latestYear - 2, latestYear - 3, latestYear - 4]) : new Set()
  );
  const [showAllYears, setShowAllYears] = useState(false);
  const [hoverMonth, setHoverMonth] = useState(null); // 1-12 or null
  const [pinnedPoints, setPinnedPoints] = useState([]); // [{year, month, value}, ...] up to 2
  const [zoomRange, setZoomRange] = useState(null); // [startMonth, endMonth] or null
  const [brushStart, setBrushStart] = useState(null); // month where mousedown happened
  const [brushEnd, setBrushEnd] = useState(null);
  const svgRef = useRef(null);

  const metric = METRICS.find(m => m.key === metricKey) || METRICS[0];

  // ── Build series: for each visible or dimmed year, an array of {month, value} points ──
  const seriesByYear = useMemo(() => {
    const out = {};
    const yearsInPlay = new Set([...visibleYears, ...dimmedYears]);
    for (const y of yearsInPlay) {
      const points = [];
      for (let m = 1; m <= 12; m++) {
        const row = byStat[metricKey]?.[`${y}-${m}`];
        if (row && row.value != null) points.push({ month: m, value: row.value });
      }
      if (points.length) out[y] = points;
    }
    return out;
  }, [byStat, metricKey, visibleYears, dimmedYears]);

  // ── Year color palette ──
  // Current year green, prior blue, older grays rotating through subtle hues
  const colorForYear = (y) => {
    if (!latestYear) return "#4a6e4a";
    if (y === latestYear) return "#4a6e4a";     // sb-green
    if (y === latestYear - 1) return "#6b9bd2"; // sb-blue
    if (y === latestYear - 2) return "#c47b5a"; // terracotta
    if (y === latestYear - 3) return "#8e7cc3"; // muted purple
    if (y === latestYear - 4) return "#a98f6e"; // tan
    // 5+ years back, cycle through medium grays
    const grayShades = ["#94a3b8", "#a0a8b0", "#8a9098", "#b0b0b0"];
    return grayShades[(latestYear - y - 5) % grayShades.length];
  };

  // ── Dimensions ──
  const chartW = isMobile ? 360 : 780;
  const chartH = isMobile ? 260 : 320;
  const pad = { t: 20, r: 16, b: 40, l: 68 };
  const plotW = chartW - pad.l - pad.r;
  const plotH = chartH - pad.t - pad.b;

  // ── Y scale: cover all visible + dimmed years ──
  const allValues = Object.values(seriesByYear).flatMap(pts => pts.map(p => p.value));
  const yMin = allValues.length ? Math.min(...allValues) * 0.985 : 0;
  const yMax = allValues.length ? Math.max(...allValues) * 1.015 : 1;
  const yRange = yMax - yMin || 1;

  // ── X scale: respects zoom range if set ──
  const [xStart, xEnd] = zoomRange || [1, 12];
  const xSpan = xEnd - xStart || 1;
  const xScale = (m) => pad.l + ((m - xStart) / xSpan) * plotW;
  const yScale = (v) => pad.t + plotH * (1 - (v - yMin) / yRange);
  const xInverse = (px) => {
    const t = (px - pad.l) / plotW;
    return Math.round(xStart + t * xSpan);
  };

  const pathFor = (points) => {
    if (!points.length) return "";
    const visible = points.filter(p => p.month >= xStart && p.month <= xEnd);
    if (!visible.length) return "";
    return visible.map((p, i) => `${i === 0 ? "M" : "L"}${xScale(p.month)},${yScale(p.value)}`).join(" ");
  };

  const MON_LONG = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const MON_SHORT = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

  // ── Toggle year visibility on chip click ──
  const toggleYear = (y) => {
    // Removing a year from either set = hide; adding = promote to visible
    setVisibleYears(prev => {
      const next = new Set(prev);
      if (next.has(y)) {
        next.delete(y);
      } else {
        next.add(y);
        // If it was dimmed, remove from dimmed
        setDimmedYears(dim => {
          if (dim.has(y)) { const d = new Set(dim); d.delete(y); return d; }
          return dim;
        });
      }
      return next;
    });
  };

  const resetSelection = () => {
    if (!latestYear) return;
    setVisibleYears(new Set([latestYear, latestYear - 1]));
    setDimmedYears(new Set([latestYear - 2, latestYear - 3, latestYear - 4]));
    setPinnedPoints([]);
    setZoomRange(null);
    setShowAllYears(false);
  };

  // ── Mouse handlers on the plot area ──
  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = chartW / rect.width;
    const px = (e.clientX - rect.left) * scaleX;
    if (px < pad.l || px > chartW - pad.r) { setHoverMonth(null); return; }
    const m = xInverse(px);
    if (m >= xStart && m <= xEnd) {
      setHoverMonth(m);
      if (brushStart != null) setBrushEnd(m);
    } else {
      setHoverMonth(null);
    }
  };

  const handleMouseLeave = () => {
    setHoverMonth(null);
    setBrushStart(null);
    setBrushEnd(null);
  };

  const handleMouseDown = (e) => {
    if (hoverMonth == null) return;
    // Record brush start — if user drags, we'll zoom; if they don't drag, we'll pin on mouseup
    setBrushStart(hoverMonth);
    setBrushEnd(hoverMonth);
  };

  const handleMouseUp = (e) => {
    if (brushStart == null) return;
    const start = Math.min(brushStart, brushEnd ?? brushStart);
    const end = Math.max(brushStart, brushEnd ?? brushStart);
    if (end - start >= 1) {
      // Dragged across at least 1 month → zoom
      setZoomRange([start, end]);
      setPinnedPoints([]); // clear pins when zooming
    } else {
      // No drag → treat as click-to-pin at this month
      // Pin the current-year point at this month (most useful anchor)
      const pinYear = latestYear;
      const row = byStat[metricKey]?.[`${pinYear}-${hoverMonth}`];
      if (row && row.value != null) {
        setPinnedPoints(prev => {
          const pt = { year: pinYear, month: hoverMonth, value: row.value };
          if (prev.length === 0) return [pt];
          if (prev.length === 1) {
            // Second click: check if same month → unpin instead
            if (prev[0].month === pt.month && prev[0].year === pt.year) return [];
            return [...prev, pt];
          }
          // Already have 2 pins: reset to just this one
          return [pt];
        });
      }
    }
    setBrushStart(null);
    setBrushEnd(null);
  };

  // ── Format helpers ──
  const fmtYoyPct = (cur, prior) => {
    if (cur == null || prior == null || prior === 0) return null;
    return ((cur - prior) / prior) * 100;
  };
  const yoyColor = (p) => p == null ? "#999" : (p > 0 ? "#059669" : p < 0 ? "#DC2626" : "#60735f");

  // ── Legend years sorted descending (newest first) ──
  const allVisibleYears = Array.from(new Set([...visibleYears, ...dimmedYears])).sort((a, b) => b - a);

  // ── Hover tooltip content: show each visible year's value at hoverMonth + YoY ──
  const tooltipRows = [];
  if (hoverMonth != null) {
    for (const y of allVisibleYears) {
      const row = byStat[metricKey]?.[`${y}-${hoverMonth}`];
      const priorRow = byStat[metricKey]?.[`${y - 1}-${hoverMonth}`];
      if (row && row.value != null) {
        const yoy = priorRow ? fmtYoyPct(row.value, priorRow.value) : null;
        tooltipRows.push({
          year: y,
          value: row.value,
          color: colorForYear(y),
          isDimmed: dimmedYears.has(y) && !visibleYears.has(y),
          yoy,
        });
      }
    }
  }

  // Brush highlight range (during drag)
  const brushActive = brushStart != null && brushEnd != null && brushStart !== brushEnd;
  const brushA = brushActive ? Math.min(brushStart, brushEnd) : null;
  const brushB = brushActive ? Math.max(brushStart, brushEnd) : null;

  // Tooltip position — pin to right side if hover is on left half, else left side
  const tooltipOnLeft = hoverMonth != null && xScale(hoverMonth) > pad.l + plotW / 2;

  return (
    <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: 16, marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a", fontFamily: "'Lora','Georgia',serif" }}>
          {selectedState} Dairy — {metric.label} by Year
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {zoomRange && (
            <button onClick={() => setZoomRange(null)}
              style={{ padding: "4px 10px", fontSize: 11, background: "#f4f8f4", color: "#4a6e4a", border: "1px solid #d0ddd0", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}>
              Reset zoom ({MON_LONG[xStart]}–{MON_LONG[xEnd]})
            </button>
          )}
          {pinnedPoints.length > 0 && (
            <button onClick={() => setPinnedPoints([])}
              style={{ padding: "4px 10px", fontSize: 11, background: "#f4f8f4", color: "#4a6e4a", border: "1px solid #d0ddd0", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}>
              Clear pins
            </button>
          )}
          <button onClick={resetSelection}
            style={{ padding: "4px 10px", fontSize: 11, background: "transparent", color: "#60735f", border: "1px solid #d0ddd0", borderRadius: 4, cursor: "pointer" }}>
            Reset
          </button>
        </div>
      </div>

      {/* Metric pills */}
      <div style={{ display: "flex", gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
        {METRICS.map(m => {
          const active = m.key === metricKey;
          return (
            <button key={m.key} onClick={() => { setMetricKey(m.key); setPinnedPoints([]); }}
              style={{
                padding: "6px 12px", fontSize: 12,
                fontWeight: active ? 700 : 500,
                background: active ? "#4a6e4a" : "#f4f8f4",
                color: active ? "#fff" : "#4a6e4a",
                border: "1px solid " + (active ? "#4a6e4a" : "#d0ddd0"),
                borderRadius: 6, cursor: "pointer",
              }}>{m.label}</button>
          );
        })}
      </div>

      {/* Year chips */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "#60735f", fontWeight: 600, marginRight: 4 }}>Years:</span>
        {allYears.slice().reverse().map(y => {
          const isVisible = visibleYears.has(y);
          const isDimmed = dimmedYears.has(y) && !isVisible;
          const isShown = isVisible || isDimmed;
          const shouldRender = showAllYears || isShown;
          if (!shouldRender) return null;
          const color = colorForYear(y);
          return (
            <button key={y} onClick={() => toggleYear(y)}
              style={{
                padding: "4px 10px", fontSize: 11,
                fontWeight: isVisible ? 700 : 500,
                background: isVisible ? color : "#fff",
                color: isVisible ? "#fff" : (isDimmed ? "#60735f" : "#bbb"),
                border: `1.5px solid ${isVisible ? color : (isDimmed ? "#d0ddd0" : "#e5e5e5")}`,
                borderRadius: 12, cursor: "pointer",
                opacity: isDimmed && !isVisible ? 0.7 : 1,
                transition: "all 0.1s",
              }}>{y}</button>
          );
        })}
        {!showAllYears && allYears.some(y => !visibleYears.has(y) && !dimmedYears.has(y)) && (
          <button onClick={() => setShowAllYears(true)}
            style={{ padding: "4px 10px", fontSize: 11, background: "transparent", color: "#4a6e4a", border: "1px dashed #d0ddd0", borderRadius: 12, cursor: "pointer" }}>
            + More years
          </button>
        )}
      </div>

      {/* Chart area */}
      <div style={{ position: "relative" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${chartW} ${chartH}`}
          style={{ width: "100%", height: "auto", maxWidth: chartW, display: "block", cursor: hoverMonth != null ? "crosshair" : "default", userSelect: "none" }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          {/* Gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map(t => {
            const val = yMin + yRange * (1 - t);
            const y = pad.t + plotH * t;
            return (
              <g key={t}>
                <line x1={pad.l} y1={y} x2={chartW - pad.r} y2={y} stroke="#e0e8e0" strokeWidth={1} />
                <text x={pad.l - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#60735f">{metric.yAxisFmt(val)}</text>
              </g>
            );
          })}

          {/* Brush highlight (during drag) */}
          {brushActive && brushA != null && brushB != null && (
            <rect x={xScale(brushA)} y={pad.t} width={xScale(brushB) - xScale(brushA)} height={plotH}
              fill="#4a6e4a" opacity={0.08} />
          )}

          {/* Dimmed lines first (drawn underneath) */}
          {Array.from(dimmedYears).filter(y => !visibleYears.has(y)).map(y => {
            const pts = seriesByYear[y];
            if (!pts || !pts.length) return null;
            return (
              <g key={`dim-${y}`}>
                <path d={pathFor(pts)} fill="none" stroke={colorForYear(y)} strokeWidth={1.2} strokeLinejoin="round" opacity={0.3} />
              </g>
            );
          })}

          {/* Visible lines on top, in order so the current year renders last */}
          {Array.from(visibleYears).sort((a, b) => a - b).map(y => {
            const pts = seriesByYear[y];
            if (!pts || !pts.length) return null;
            const c = colorForYear(y);
            const isLatest = y === latestYear;
            return (
              <g key={`vis-${y}`}>
                <path d={pathFor(pts)} fill="none" stroke={c} strokeWidth={isLatest ? 2.5 : 2} strokeLinejoin="round" />
                {pts.filter(p => p.month >= xStart && p.month <= xEnd).map(p => (
                  <circle key={`${y}-${p.month}`} cx={xScale(p.month)} cy={yScale(p.value)} r={isLatest ? 3 : 2.5} fill={c} />
                ))}
              </g>
            );
          })}

          {/* Hover crosshair */}
          {hoverMonth != null && !brushActive && (
            <line x1={xScale(hoverMonth)} y1={pad.t} x2={xScale(hoverMonth)} y2={pad.t + plotH}
              stroke="#4a6e4a" strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
          )}

          {/* Pinned points */}
          {pinnedPoints.map((pt, i) => (
            <g key={`pin-${i}`}>
              <circle cx={xScale(pt.month)} cy={yScale(pt.value)} r={6} fill="none" stroke="#d97706" strokeWidth={2} />
              <text x={xScale(pt.month)} y={yScale(pt.value) - 10} textAnchor="middle" fontSize={9} fill="#d97706" fontWeight={700}>
                {MON_LONG[pt.month]} {pt.year}
              </text>
            </g>
          ))}

          {/* Delta line between 2 pins */}
          {pinnedPoints.length === 2 && (() => {
            const [a, b] = pinnedPoints;
            const midX = (xScale(a.month) + xScale(b.month)) / 2;
            const midY = (yScale(a.value) + yScale(b.value)) / 2;
            const delta = b.value - a.value;
            const pctDelta = a.value ? (delta / a.value) * 100 : null;
            const label = `${delta >= 0 ? "+" : ""}${metric.fmt(Math.abs(delta))} (${pctDelta != null ? `${pctDelta >= 0 ? "+" : ""}${pctDelta.toFixed(1)}%` : "—"})`;
            return (
              <g>
                <line x1={xScale(a.month)} y1={yScale(a.value)} x2={xScale(b.month)} y2={yScale(b.value)}
                  stroke="#d97706" strokeWidth={1.5} strokeDasharray="3,2" />
                <rect x={midX - 55} y={midY - 22} width={110} height={18} rx={4}
                  fill="#fff" stroke="#d97706" strokeWidth={1} />
                <text x={midX} y={midY - 9} textAnchor="middle" fontSize={10} fill="#d97706" fontWeight={700}>
                  {label}
                </text>
              </g>
            );
          })()}

          {/* X axis month labels */}
          {Array.from({ length: xEnd - xStart + 1 }, (_, i) => xStart + i).map(m => {
            const every = xSpan <= 6 ? 1 : (xSpan <= 9 ? 1 : (isMobile ? 2 : 1));
            if ((m - xStart) % every !== 0 && m !== xEnd) return null;
            return (
              <text key={m} x={xScale(m)} y={chartH - pad.b + 16} textAnchor="middle" fontSize={10} fill="#60735f">
                {xSpan >= 6 ? MON_SHORT[m - 1] : MON_LONG[m]}
              </text>
            );
          })}

          {/* Axis labels */}
          <text x={pad.l + plotW / 2} y={chartH - 4} textAnchor="middle" fontSize={10} fill="#60735f" fontWeight={600}>
            Month
          </text>
        </svg>

        {/* Tooltip */}
        {hoverMonth != null && tooltipRows.length > 0 && !brushActive && (
          <div style={{
            position: "absolute",
            top: 10,
            [tooltipOnLeft ? "left" : "right"]: 16,
            background: "rgba(255,255,255,0.98)",
            border: "1px solid #d0ddd0",
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 11,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            pointerEvents: "none",
            minWidth: 180,
          }}>
            <div style={{ fontWeight: 700, color: "#1a2e1a", marginBottom: 4, borderBottom: "1px solid #f0f4f0", paddingBottom: 4 }}>
              {MON_LONG[hoverMonth]}
            </div>
            {tooltipRows.map(r => (
              <div key={r.year} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0", opacity: r.isDimmed ? 0.6 : 1 }}>
                <span style={{ color: r.color, fontWeight: 700 }}>{r.year}</span>
                <span style={{ color: "#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{metric.fmt(r.value)}</span>
                {r.yoy != null && (
                  <span style={{ color: yoyColor(r.yoy), fontVariantNumeric: "tabular-nums", fontSize: 10 }}>
                    {r.yoy >= 0 ? "+" : ""}{r.yoy.toFixed(1)}%
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Interaction hints */}
      <div style={{ fontSize: 10, color: "#aaa", marginTop: 8, lineHeight: 1.5 }}>
        Click a month to pin it · click a second to see delta · drag horizontally to zoom · chips above toggle years
      </div>
    </div>
  );
}

function DRPQuotingView({ isMobile }) {
  const [rows, setRows] = useState([]);        // flat rows for selected date
  const [allHistory, setAllHistory] = useState([]); // all rows in DB for market context
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    function tryLoad() {
      attempts++;
      const headers = getAuthHeaders();

      (async () => {
        try {
          setLoading(true);

          // Get distinct dates
          const datesRes = await fetch(
            `${SUPABASE_URL}/rest/v1/drp_board?select=price_date&order=price_date.desc&limit=500`,
            { headers }
          );

          let dates = [];
          if (datesRes.ok) {
            const data = await datesRes.json();
            if (Array.isArray(data)) {
              const seen = new Set();
              dates = data
                .filter(r => { if (seen.has(r.price_date)) return false; seen.add(r.price_date); return true; })
                .map(r => r.price_date);
            }
          } else if (datesRes.status === 401 && attempts < maxAttempts) {
            setTimeout(tryLoad, 500);
            return;
          }

          setAvailableDates(dates);

          if (dates.length === 0) {
            setRows([]);
            setLoading(false);
            return;
          }

          const latestDate = dates[0];
          setSelectedDate(latestDate);

          // Load today's rows + full history in parallel
          const [todayRows, historyData] = await Promise.all([
            loadDateRows(latestDate, headers),
            loadAllHistory(headers),
          ]);
          setRows(todayRows);
          setAllHistory(historyData);

        } catch (e) {
          console.error('DRP load error:', e);
        } finally {
          setLoading(false);
        }
      })();
    }

    setTimeout(tryLoad, 300);
  }, []);

  async function loadDateRows(date, headers) {
    const h = headers || getAuthHeaders();
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/drp_board?price_date=eq.${date}&order=quarter.asc&order=milk_class.asc`,
      { headers: h }
    );
    return res.ok ? await res.json() : [];
  }

  // Fetch all rows from DB for market context analysis
  async function loadAllHistory(headers) {
    const h = headers || getAuthHeaders();
    const res = await fetch(
      (() => {
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - 1);
        const cutoffStr = cutoff.toISOString().slice(0, 10);
        return `${SUPABASE_URL}/rest/v1/drp_board?select=price_date,milk_class,quarter,net_floor,price&price_date=gte.${cutoffStr}&order=price_date.asc&limit=5000`;
      })(),
      { headers: h }
    );
    return res.ok ? await res.json() : [];
  }

  async function loadDate(date, headers) {
    const todayRows = await loadDateRows(date, headers);
    setRows(todayRows);
  }

  async function handleDateChange(date) {
    setSelectedDate(date);
    setLoading(true);
    await loadDate(date);
    setLoading(false);
  }

  // Build per-quarter paired structure from flat rows
  // Returns array of { quarter, iii: row|null, iv: row|null }
  function buildQuarterPairs() {
    const map = {};
    for (const row of rows) {
      if (!map[row.quarter]) map[row.quarter] = { quarter: row.quarter, iii: null, iv: null };
      if (row.milk_class === 'Class III') map[row.quarter].iii = row;
      if (row.milk_class === 'Class IV')  map[row.quarter].iv  = row;
    }
    // Chronological sort: parse Q3-26 → 2026Q3, then compare
    function qSortKey(q) {
      let m = q.match(/^Q(\d)-(\d{2})$/);
      if (m) return `20${m[2]}Q${m[1]}`;
      m = q.match(/^(\d{4})-Q(\d)$/);
      if (m) return `${m[1]}Q${m[2]}`;
      return q;
    }
    return Object.values(map).sort((a, b) => qSortKey(a.quarter).localeCompare(qSortKey(b.quarter)));
  }

  const fmtD = (v) => v != null ? '$' + Number(v).toFixed(2) : '—';
  const fmtP = (v) => v != null ? '$' + Number(v).toFixed(2) : '—';

  function qLabel(q) {
    if (!q) return q;
    // Handle both "Q3-26" and "2026-Q3" formats
    let m = q.match(/^Q(\d)-(\d{2})$/);
    if (m) return `Q${m[1]} '${m[2]}`;
    m = q.match(/^(\d{4})-Q(\d)$/);
    if (m) return `Q${m[2]} '${m[1].slice(2)}`;
    return q;
  }

  function formatDateHeader(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }

  if (loading && rows.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🥛</div>
        <div style={{ fontSize: 15, color: '#60735f', fontWeight: 600 }}>Loading DRP pricing data...</div>
      </div>
    );
  }

  if (rows.length === 0 && !loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🥛</div>
        <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 22, fontWeight: 700, color: '#1a2e1a', marginBottom: 8 }}>DRP Quoting</div>
        <div style={{ fontSize: 14, color: '#60735f', maxWidth: 400, margin: '0 auto', lineHeight: 1.7 }}>
          No DRP pricing data yet. Once the Ever.Ag daily email pipeline is connected, prices will appear here automatically.
        </div>
        <div style={{ marginTop: 24, padding: '16px 24px', background: '#f8faf8', borderRadius: 10, border: '1px solid #dde5dd', display: 'inline-block', textAlign: 'left' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1a2e1a', marginBottom: 8 }}>Setup Status</div>
          <div style={{ fontSize: 12, color: '#60735f' }}>✅ drp_daily_prices table created</div>
          <div style={{ fontSize: 12, color: '#60735f' }}>✅ Historical data loaded (Jan–Mar 2026)</div>
          <div style={{ fontSize: 12, color: '#60735f' }}>⏳ Apps Script pipeline deploying</div>
        </div>
      </div>
    );
  }

  const thStyle = {
    textAlign: 'right',
    padding: isMobile ? '7px 8px' : '9px 14px',
    fontWeight: 700,
    color: '#60735f',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    background: '#f4f8f4',
    borderBottom: '1px solid #e4ece4',
  };
  const tdStyle = {
    padding: isMobile ? '9px 8px' : '10px 14px',
    textAlign: 'right',
    fontVariantNumeric: "tabular-nums",
    fontSize: isMobile ? 12 : 13,
    color: '#1a2e1a',
  };
  const rowBg = (i) => ({
    borderTop: '1px solid #f0f4f0',
    background: i % 2 === 0 ? '#fff' : '#fafcfa',
  });

  function qSortKey(q) {
    let m = q.match(/^Q(\d)-(\d{2})$/);
    if (m) return `20${m[2]}Q${m[1]}`;
    m = q.match(/^(\d{4})-Q(\d)$/);
    if (m) return `${m[1]}Q${m[2]}`;
    return q;
  }

  function renderTable(milkClass) {
    const isIII = milkClass === 'Class III';
    const accent = isIII ? '#1a4a7a' : '#2d6b4a';
    const headerAccent = isIII ? '#e8f0f8' : '#e8f4ec';
    const headerBorder = isIII ? '#c8dae8' : '#c0dcc8';
    const classRows = rows.filter(r => r.milk_class === milkClass).sort((a,b) => qSortKey(a.quarter).localeCompare(qSortKey(b.quarter)));

    return (
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #dde5dd', marginBottom: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${headerBorder}`, background: headerAccent, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 14, fontWeight: 700, color: '#1a2e1a' }}>{milkClass}</span>
          <span style={{ fontSize: 10, color: '#60735f', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>95% Coverage</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left' }}>Qtr</th>
              <th style={thStyle}>Price</th>
              <th style={thStyle}>95% Floor</th>
              <th style={{ ...thStyle, color: '#b03020' }}>Premium</th>
              <th style={{ ...thStyle, color: accent }}>Net Floor</th>
            </tr>
          </thead>
          <tbody>
            {classRows.map((row, i) => {
              const floor95 = row.price != null ? +(row.price * 0.95).toFixed(2) : null;
              return (
                <tr key={row.quarter} style={rowBg(i)}>
                  <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 700, fontFamily: 'inherit', color: '#1a2e1a' }}>{qLabel(row.quarter)}</td>
                  <td style={tdStyle}>{fmtD(row.price)}</td>
                  <td style={tdStyle}>{fmtD(floor95)}</td>
                  <td style={{ ...tdStyle, color: '#b03020' }}>{fmtP(row.premium)}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: accent, fontSize: isMobile ? 13 : 14 }}>{fmtD(floor95 != null && row.premium != null ? +(floor95 - row.premium).toFixed(2) : row.net_floor)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ height: 3, background: `linear-gradient(to right, ${headerBorder}, transparent)` }} />
      </div>
    );
  }

  const pairs = buildQuarterPairs();

  return (
    <div style={{ maxWidth: 540, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#1a2e1a', marginBottom: 2 }}>DRP Daily Prices</div>
        <div style={{ fontSize: 13, color: '#60735f', marginBottom: 6 }}>{formatDateHeader(selectedDate)}</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#677167', background: '#f4f8f4', borderRadius: 20, padding: '3px 12px', border: '1px solid #dde5dd' }}>
          <span>New York</span><span style={{ color: '#cdd8cd' }}>·</span><span>95% Coverage</span><span style={{ color: '#cdd8cd' }}>·</span><span>Class Pricing</span>
        </div>
      </div>

      {/* Date picker */}
      {availableDates.length > 1 && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <select
            value={selectedDate || ''}
            onChange={e => handleDateChange(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #dde5dd', fontSize: 12, fontWeight: 600, color: '#1a2e1a', background: '#fff', cursor: 'pointer' }}
          >
            {availableDates.map(d => (
              <option key={d} value={d}>{new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</option>
            ))}
          </select>
        </div>
      )}

      {renderTable('Class III')}
      {renderTable('Class IV')}

      {/* Market Context */}
      <DRPMarketContext pairs={pairs} allHistory={allHistory} isMobile={isMobile} selectedDate={selectedDate} />

    </div>
  );
}

// DRP MARKET CONTEXT — Live analysis from DB data
// Receives pairs: { quarter, iii, iv } and allHistory: all rows from drp_daily_prices
// ─────────────────────────────────────────────────────────────────────────────
function DRPMarketContext({ pairs, allHistory, isMobile, selectedDate }) {
  const [open, setOpen] = useState(false);

  function qLabel(q) {
    if (!q) return q;
    let m = q.match(/^Q(\d)-(\d{2})$/);
    if (m) return `Q${m[1]} '${m[2]}`;
    m = q.match(/^(\d{4})-Q(\d)$/);
    if (m) return `Q${m[2]} '${m[1].slice(2)}`;
    return q;
  }

  function daysBeforeStart(quarter) {
    let yr, qn;
    let m = quarter && quarter.match(/^Q(\d)-(\d{2})$/);
    if (m) { qn = parseInt(m[1]); yr = 2000 + parseInt(m[2]); }
    else { m = quarter && quarter.match(/^(\d{4})-Q(\d)$/); if (m) { yr = parseInt(m[1]); qn = parseInt(m[2]); } else return null; }
    const start = new Date(yr, [0,3,6,9][qn-1], 1);
    return Math.floor((start - new Date()) / 86400000);
  }

  // Normalize quarter to YYYY-QN regardless of input format (handles both Q3-26 and 2026-Q3)
  function normQ(q) {
    if (!q) return q;
    let m = q.match(/^Q(\d)-(\d{2})$/);
    if (m) return `20${m[2]}-Q${m[1]}`;
    m = q.match(/^(\d{4})-Q(\d)$/);
    if (m) return q;
    return q;
  }

  function buildStats(milkClass, quarter, todayNF) {
    if (!allHistory || allHistory.length === 0 || todayNF == null) return null;
    // Rolling 12-month window — anchor to selectedDate not browser clock
    const base = selectedDate ? new Date(selectedDate + 'T12:00:00') : new Date();
    const cutoff = new Date(base);
    cutoff.setFullYear(cutoff.getFullYear() - 1);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    const normQuarter = normQ(quarter);
    const vals = allHistory
      .filter(r => r.milk_class === milkClass && normQ(r.quarter) === normQuarter && r.net_floor != null && r.price_date >= cutoffStr)
      .sort((a, b) => a.price_date.localeCompare(b.price_date));
    if (vals.length < 1) return null;
    const floors = vals.map(r => +r.net_floor);
    const min = Math.min(...floors, todayNF);
    const max = Math.max(...floors, todayNF);
    const sorted = [...floors].sort((a,b) => a-b);
    const median = sorted[Math.floor(sorted.length / 2)];
    // Rank todayNF against historical distribution (don't add today to the array)
    const pct = Math.round((floors.filter(v => v <= todayNF).length / (floors.length + 1)) * 100);
    // Use actual calendar date lookups for momentum — find most recent row at least N calendar days ago
    function nfDaysAgo(nDays) {
      const base = selectedDate ? new Date(selectedDate + 'T12:00:00') : new Date();
      const target = new Date(base);
      target.setDate(target.getDate() - nDays);
      const targetStr = target.toISOString().slice(0, 10);
      // Walk backwards through vals (sorted ascending) to find most recent row <= targetStr
      // vals has price_date strings like '2026-03-12' — compare directly
      for (let i = vals.length - 1; i >= 0; i--) {
        const pd = vals[i].price_date ? vals[i].price_date.slice(0, 10) : null;
        if (pd && pd <= targetStr) return +vals[i].net_floor;
      }
      return null;
    }
    const prior3NF  = nfDaysAgo(3);
    const prior7NF  = nfDaysAgo(7);
    const prior30NF = nfDaysAgo(30);
    const momDiff = prior3NF  != null ? +(todayNF - prior3NF).toFixed(2)  : null;
    const momDays = 3;
    const chg7  = prior7NF  != null ? +(todayNF - prior7NF).toFixed(2)  : null;
    const chg30 = prior30NF != null ? +(todayNF - prior30NF).toFixed(2) : null;
    const rangePos = max > min ? Math.round(((todayNF - min) / (max - min)) * 100) : 50;
    const spark = floors.slice(-20);
    const sMin = Math.min(...spark); const sMax = Math.max(...spark);
    const sparkNorm = spark.map(v => sMax > sMin ? Math.round(((v - sMin) / (sMax - sMin)) * 100) : 50);
    return { min, max, median, pct, todayNF, momDiff, momDays, chg7, chg30, rangePos, sparkNorm, nObs: floors.length };
  }

  if (!pairs || pairs.length === 0) return null;
  const totalRows = allHistory ? allHistory.length : 0;

  const pctColor = (p) => p >= 65 ? '#2d7a2d' : p >= 40 ? '#c07010' : '#c0392b';
  const pctBg    = (p) => p >= 65 ? '#edf7ed' : p >= 40 ? '#fdf3e7' : '#fdf0ee';
  const chgColor = (v) => v == null ? '#aaa' : v > 0 ? '#2d7a2d' : v < 0 ? '#c0392b' : '#60735f';
  const chgArrow = (v) => v == null ? '' : v > 0 ? '↑' : v < 0 ? '↓' : '→';

  function daysChip(quarter) {
    const d = daysBeforeStart(quarter);
    if (d == null) return null;
    if (d < 0) return { label: 'Quarter active', color: '#2d7a2d', bg: '#edf7ed' };
    if (d <= 15) return { label: `⚑ ${d}d to close`, color: '#b03020', bg: '#fdf0ee' };
    if (d <= 60) return { label: `${d}d to start`, color: '#c07010', bg: '#fdf3e7' };
    return { label: `${d}d to start`, color: '#60735f', bg: '#f4f8f4' };
  }

  function sparkPath(norm, w, h) {
    if (!norm || norm.length < 2) return '';
    const step = w / (norm.length - 1);
    return norm.map((v, i) => {
      const x = i * step; const y = h - (v / 100) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
  }

  return (
    <div style={{ borderRadius: 12, border: '1px solid #dde5dd', marginBottom: 20, overflow: 'hidden', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ padding: '12px 16px', background: open ? '#fff' : '#f8faf8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none', borderBottom: open ? '1px solid #eef2ee' : 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>📈</span>
          <span style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 15, fontWeight: 700, color: '#1a2e1a' }}>Market Context</span>
          {totalRows > 0 && (
            <span style={{ fontSize: 10, color: '#677167', background: '#f0f4f0', borderRadius: 10, padding: '2px 7px', fontWeight: 600 }}>
              Rolling 12 months
            </span>
          )}
        </div>
        <span style={{ fontSize: 14, color: '#60735f' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ padding: isMobile ? '12px 8px' : '16px 14px' }}>
          {pairs.map(({ quarter, iii, iv }) => {
            const chip = daysChip(quarter);
            const statsIII = buildStats('Class III', quarter, iii ? +(iii.price * 0.95 - iii.premium).toFixed(2) : null);
            const statsIV  = buildStats('Class IV',  quarter, iv  ? +(iv.price  * 0.95 - iv.premium).toFixed(2)  : null);
            if (!statsIII && !statsIV) return null;

            return (
              <div key={quarter} style={{ marginBottom: 16, borderRadius: 10, border: '1px solid #eef2ee', overflow: 'auto' }}>
                <div style={{ padding: '8px 14px', background: '#f8faf8', borderBottom: '1px solid #eef2ee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 15, fontWeight: 700, color: '#1a2e1a' }}>{qLabel(quarter)}</span>
                  {chip && <span style={{ fontSize: 10, fontWeight: 700, color: chip.color, background: chip.bg, borderRadius: 10, padding: '3px 9px' }}>{chip.label}</span>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, overflowX: 'auto', minWidth: 320 }}>
                  {[
                    { label: 'Class III', stats: statsIII, accent: '#1a4a7a' },
                    { label: 'Class IV',  stats: statsIV,  accent: '#2d6b4a' },
                  ].map(({ label, stats, accent }, ci) => (
                    <div key={label} style={{ padding: '12px 12px', borderLeft: ci === 1 ? '1px solid #eef2ee' : 'none' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
                      {stats ? (
                        <>
                          {/* Net floor + 3d momentum */}
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 8 }}>
                            <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 17, fontWeight: 700, color: '#1a2e1a' }}>${stats.todayNF.toFixed(2)}</span>
                            {stats.momDiff !== null && (
                              <span style={{ fontSize: 11, fontWeight: 700, color: chgColor(stats.momDiff) }}>
                                {chgArrow(stats.momDiff)}{stats.momDiff !== 0 ? ` $${Math.abs(stats.momDiff).toFixed(2)}` : ' flat'}
                                <span style={{ fontSize: 10, color: '#bbb', marginLeft: 2 }}>{stats.momDays}d</span>
                              </span>
                            )}
                          </div>

                          {/* Sparkline — last 20 days */}
                          {stats.sparkNorm.length >= 3 && (() => {
                            const W = (stats.sparkNorm.length - 1) * 6;
                            const lastX = (stats.sparkNorm.length - 1) * 6;
                            const lastY = 24 - (stats.sparkNorm[stats.sparkNorm.length - 1] / 100) * 24;
                            return (
                              <div style={{ marginBottom: 8 }}>
                                <svg width="100%" height="28" viewBox={`0 0 ${W} 28`} preserveAspectRatio="none" style={{ display: 'block' }}>
                                  <path d={sparkPath(stats.sparkNorm, W, 24)} fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
                                  <circle cx={lastX} cy={lastY} r="2.5" fill={accent} />
                                </svg>
                              </div>
                            );
                          })()}

                          {/* YTD range bar */}
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                              <span style={{ fontSize: 10, color: '#aaa' }}>YTD low ${stats.min.toFixed(2)}</span>
                              <span style={{ fontSize: 10, color: '#aaa' }}>high ${stats.max.toFixed(2)}</span>
                            </div>
                            <div style={{ position: 'relative', height: 6, background: '#eef2ee', borderRadius: 3 }}>
                              <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${stats.rangePos}%`, background: `linear-gradient(to right, #dde8dd, ${accent})`, borderRadius: 3 }} />
                              <div style={{ position: 'absolute', top: '50%', left: `${stats.rangePos}%`, transform: 'translate(-50%, -50%)', width: 10, height: 10, borderRadius: '50%', background: accent, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            </div>
                          </div>

                          {/* Percentile pill */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 10, color: '#aaa' }}>{stats.nObs} days of data</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: pctColor(stats.pct), background: pctBg(stats.pct), borderRadius: 8, padding: '2px 7px' }}>{ordinal(stats.pct)} pct of YTD range</span>
                          </div>

                          {/* 7d / 30d / median chips */}
                          <div style={{ display: 'flex', gap: 5 }}>
                            {stats.chg7 !== null && (
                              <div style={{ flex: 1, background: '#f8faf8', borderRadius: 5, padding: '4px 6px', textAlign: 'center', border: '1px solid #eef2ee' }}>
                                <div style={{ fontSize: 9, color: '#aaa', marginBottom: 1 }}>7d</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: chgColor(stats.chg7) }}>{chgArrow(stats.chg7)}{stats.chg7 !== 0 ? `$${Math.abs(stats.chg7).toFixed(2)}` : 'flat'}</div>
                              </div>
                            )}
                            {stats.chg30 !== null && (
                              <div style={{ flex: 1, background: '#f8faf8', borderRadius: 5, padding: '4px 6px', textAlign: 'center', border: '1px solid #eef2ee' }}>
                                <div style={{ fontSize: 9, color: '#aaa', marginBottom: 1 }}>30d</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: chgColor(stats.chg30) }}>{chgArrow(stats.chg30)}{stats.chg30 !== 0 ? `$${Math.abs(stats.chg30).toFixed(2)}` : 'flat'}</div>
                              </div>
                            )}
                            <div style={{ flex: 1, background: '#f8faf8', borderRadius: 5, padding: '4px 6px', textAlign: 'center', border: '1px solid #eef2ee' }}>
                              <div style={{ fontSize: 9, color: '#aaa', marginBottom: 1 }}>median</div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#1a2e1a' }}>${stats.median.toFixed(2)}</div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 11, color: '#bbb', padding: '8px 0' }}>No data yet</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div style={{ textAlign: 'center', fontSize: 10, color: '#ccc', marginTop: 4 }}>
            All stats based on daily net floor observations for each specific quarter · rolling 12-month window
          </div>
        </div>
      )}
    </div>
  );
}

// DRP EMAIL LIST MANAGER — removed 2026-06-18. Email subscribers are now managed
// centrally in AlertsManagerView ("Communications") against the unified
// email_subscribers table; the DRP page's Emails tab points there.

// ─────────────────────────────────────────────────────────────────────────────
// CATTLE MARKET INTELLIGENCE — on-demand AI brief + live news (LRP analog of Milk MI).
// PORTAL-SAFE: the brief is built from market-only tables (AMS 5-area, boxed beef,
// NASS cattle-on-feed, NASS/MARS feeder, and RMA's PUBLISHED LRP rate grid) — NO
// agency book, customer, or policy data (verified 2026-06-24). Viewing the latest
// stored brief (GET /api/market?fn=cattle-brief) is open to portal LRP users;
// only GENERATING a new brief (POST) is admin-gated in _cattle-brief.js.
// Headlines via fn=cattle-news.
// ─────────────────────────────────────────────────────────────────────────────
function CattleMIView({ isMobile }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);
  const [showData, setShowData] = useState(false);
  const [news, setNews] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/market?fn=cattle-brief");
        const j = await asJson(r);
        if (alive) setBrief(j?.brief || null);
      } catch (e) { if (alive) setErr(String(e.message || e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/market?fn=cattle-news");
        const j = await asJson(r);
        if (alive) setNews(j?.ok ? (j.items || []) : []);
      } catch { if (alive) setNews([]); }
    })();
    return () => { alive = false; };
  }, []);

  async function generate() {
    setGenerating(true); setErr(null);
    try {
      const token = localStorage.getItem("cloverag_access_token");
      const r = await fetch("/api/market?fn=cattle-brief", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const j = await asJson(r);
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setBrief(j.brief);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setGenerating(false); }
  }

  const relTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  };

  const n = brief?.narrative || null;
  const card = (children, extra = {}) => (
    <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: isMobile ? 14 : 18, ...extra }}>{children}</div>
  );

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: isMobile ? "14px 4px" : "20px 0" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: isMobile ? 18 : 21, fontWeight: 700, color: "#1a2e1a" }}>
            🧠 Cattle Market Intelligence
          </div>
          <div style={{ fontSize: 12, color: "#60735f" }}>
            AI read of the cattle market &amp; LRP coverage — fed cattle, feeder, beef demand, on-feed supply
          </div>
        </div>
        {isCurrentUserAdmin() && (
          <button onClick={generate} disabled={generating}
            style={{ padding: "10px 18px", fontSize: 14, fontWeight: 700, fontFamily: "'Lora','Georgia',serif",
              color: "#fff", background: generating ? "#60735f" : "#2d4a2d", border: "none", borderRadius: 8,
              cursor: generating ? "default" : "pointer", whiteSpace: "nowrap" }}>
            {generating ? "Analyzing cattle markets…" : brief ? "↻ Regenerate" : "Generate brief"}
          </button>
        )}
      </div>

      {/* Cattle market news — real, current links from Google News RSS */}
      <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: isMobile ? 14 : 18, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 15, fontWeight: 700, color: "#1a2e1a" }}>📰 Cattle Market News</div>
          <div style={{ fontSize: 11, color: "#677167" }}>US markets · live headlines</div>
        </div>
        {news === null ? (
          <div style={{ fontSize: 13, color: "#60735f" }}>Loading headlines…</div>
        ) : news.length === 0 ? (
          <div style={{ fontSize: 13, color: "#60735f" }}>No headlines available right now.</div>
        ) : (
          <div>
            {news.map((it, i) => (
              <div key={i} style={{ padding: "9px 0", borderTop: i ? "1px solid #f0f4f0" : "none" }}>
                <a href={it.link} target="_blank" rel="noopener noreferrer"
                   style={{ fontSize: 13.5, fontWeight: 600, color: "#2d5a10", textDecoration: "none", lineHeight: 1.4 }}>
                  {it.title}
                </a>
                <div style={{ fontSize: 11, color: "#677167", marginTop: 2 }}>
                  {it.source || "—"}{it.pubDate ? ` · ${new Date(it.pubDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {generating && (
        <div style={{ fontSize: 13, color: "#4a6e4a", marginBottom: 12, fontStyle: "italic" }}>
          Reading 5-Area fed cattle, boxed beef, Cattle on Feed, the feeder index, LRP coverage prices and our open book… ~15–25 seconds.
        </div>
      )}
      {err && (
        <div style={{ background: "#fdecea", border: "1px solid #f5c6c0", borderRadius: 8, padding: 12, color: "#a3372d", fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      )}

      {loading && !brief && <div style={{ color: "#60735f", fontSize: 14 }}>Loading latest brief…</div>}

      {!loading && !brief && !generating && (
        <div style={{ background: "#f2f6f2", border: "1px dashed #c3d4c3", borderRadius: 10, padding: 28, textAlign: "center", color: "#4a5a4a" }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>🐂</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>No brief yet</div>
          <div style={{ fontSize: 13, color: "#60735f" }}>
            {isCurrentUserAdmin() ? "Hit “Generate brief” to have the agent read the cattle market." : "A market brief hasn’t been published yet — check back soon. Live headlines are above."}
          </div>
        </div>
      )}

      {n && <BriefRunDate brief={brief} canRegenerate={isCurrentUserAdmin()} what="cattle market" />}
      {n && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 12, color: "#60735f" }}>
            Data as of <strong>{brief.as_of || "—"}</strong> · generated {relTime(brief.generated_at)}
            {brief.model && isCurrentUserAdmin() ? ` · ${brief.model}` : ""}
          </div>

          {n.headline && card(
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#1a2e1a", lineHeight: 1.45, fontFamily: "'Lora','Georgia',serif" }}>
              {n.headline}
            </div>,
            { borderLeft: "4px solid #4a6e4a" }
          )}

          {Array.isArray(n.sections) && n.sections.map((s, i) => card(
            <div key={i}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#2d4a2d", marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 14, color: "#3a4a3a", lineHeight: 1.6 }}>{s.body}</div>
            </div>
          ))}

          {n.lrpTakeaway && card(
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1a4a1a", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>🐄 LRP Takeaway</div>
              <div style={{ fontSize: 14, color: "#1a3a1a", lineHeight: 1.6 }}>{n.lrpTakeaway}</div>
            </div>,
            { background: "#eaf3ea", border: "1px solid #cfe3cf" }
          )}

          {n.teaching && card(
            <div style={{ fontSize: 13.5, color: "#6a5a2a", lineHeight: 1.6, fontStyle: "italic" }}>
              <strong style={{ fontStyle: "normal" }}>📘 Learn:</strong> {n.teaching}
            </div>,
            { background: "#fbf6e8", border: "1px solid #ece2c4" }
          )}

          {Array.isArray(n.watch) && n.watch.length > 0 && card(
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#2d4a2d", marginBottom: 6 }}>👀 What to watch</div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: "#3a4a3a", lineHeight: 1.7 }}>
                {n.watch.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div>
            <button onClick={() => setShowData(v => !v)} style={{ background: "none", border: "none", color: "#60735f", fontSize: 12, cursor: "pointer", padding: "4px 0", textDecoration: "underline" }}>
              {showData ? "Hide" : "Show"} the data the agent read
            </button>
            {showData && (
              <pre style={{ background: "#1a2e1a", color: "#cfe3cf", padding: 14, borderRadius: 8, fontSize: 11, overflowX: "auto", lineHeight: 1.5, marginTop: 6 }}>
                {JSON.stringify(brief.snapshot, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Grain Market Intelligence — corn / soybeans / wheat. Live Google News headlines +
// an on-demand AI brief (admin generates; portal farmers read the latest). Mirrors
// CattleMIView. Backend: /api/market?fn=grain-news + ?fn=grain-brief. PORTAL-SAFE —
// pure market data, no agency book. See api/_grain-brief.js.
// ─────────────────────────────────────────────────────────────────────────────
function GrainMIView({ isMobile }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);
  const [showData, setShowData] = useState(false);
  const [news, setNews] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/market?fn=grain-brief");
        const j = await asJson(r);
        if (alive) setBrief(j?.brief || null);
      } catch (e) { if (alive) setErr(String(e.message || e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/market?fn=grain-news");
        const j = await asJson(r);
        if (alive) setNews(j?.ok ? (j.items || []) : []);
      } catch { if (alive) setNews([]); }
    })();
    return () => { alive = false; };
  }, []);

  async function generate() {
    setGenerating(true); setErr(null);
    try {
      const token = localStorage.getItem("cloverag_access_token");
      const r = await fetch("/api/market?fn=grain-brief", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const j = await asJson(r);
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setBrief(j.brief);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setGenerating(false); }
  }

  const relTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  };

  const n = brief?.narrative || null;
  const card = (children, extra = {}) => (
    <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: isMobile ? 14 : 18, ...extra }}>{children}</div>
  );

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: isMobile ? "14px 4px" : "20px 0" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: isMobile ? 18 : 21, fontWeight: 700, color: "#1a2e1a" }}>
            🧠 Grain Market Intelligence
          </div>
          <div style={{ fontSize: 12, color: "#60735f" }}>
            AI read of corn, soybeans &amp; wheat — futures, fund positioning, and local WNY basis
          </div>
        </div>
        {isCurrentUserAdmin() && (
          <button onClick={generate} disabled={generating}
            style={{ padding: "10px 18px", fontSize: 14, fontWeight: 700, fontFamily: "'Lora','Georgia',serif",
              color: "#fff", background: generating ? "#60735f" : "#2d4a2d", border: "none", borderRadius: 8,
              cursor: generating ? "default" : "pointer", whiteSpace: "nowrap" }}>
            {generating ? "Analyzing grain markets…" : brief ? "↻ Regenerate" : "Generate brief"}
          </button>
        )}
      </div>

      {/* Grain market news — real, current links from Google News RSS */}
      <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: isMobile ? 14 : 18, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 15, fontWeight: 700, color: "#1a2e1a" }}>📰 Grain Market News</div>
          <div style={{ fontSize: 11, color: "#677167" }}>US markets · live headlines</div>
        </div>
        {news === null ? (
          <div style={{ fontSize: 13, color: "#60735f" }}>Loading headlines…</div>
        ) : news.length === 0 ? (
          <div style={{ fontSize: 13, color: "#60735f" }}>No headlines available right now.</div>
        ) : (
          <div>
            {news.map((it, i) => (
              <div key={i} style={{ padding: "9px 0", borderTop: i ? "1px solid #f0f4f0" : "none" }}>
                <a href={it.link} target="_blank" rel="noopener noreferrer"
                   style={{ fontSize: 13.5, fontWeight: 600, color: "#2d5a10", textDecoration: "none", lineHeight: 1.4 }}>
                  {it.title}
                </a>
                <div style={{ fontSize: 11, color: "#677167", marginTop: 2 }}>
                  {it.source || "—"}{it.pubDate ? ` · ${new Date(it.pubDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {generating && (
        <div style={{ fontSize: 13, color: "#4a6e4a", marginBottom: 12, fontStyle: "italic" }}>
          Reading CBOT corn/soy/wheat futures, CFTC managed-money positioning, and local WNY basis… ~15–25 seconds.
        </div>
      )}
      {err && (
        <div style={{ background: "#fdecea", border: "1px solid #f5c6c0", borderRadius: 8, padding: 12, color: "#a3372d", fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      )}

      {loading && !brief && <div style={{ color: "#60735f", fontSize: 14 }}>Loading latest brief…</div>}

      {!loading && !brief && !generating && (
        <div style={{ background: "#f2f6f2", border: "1px dashed #c3d4c3", borderRadius: 10, padding: 28, textAlign: "center", color: "#4a5a4a" }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>🌽</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>No brief yet</div>
          <div style={{ fontSize: 13, color: "#60735f" }}>
            {isCurrentUserAdmin() ? "Hit “Generate brief” to have the agent read the grain markets." : "A market brief hasn’t been published yet — check back soon. Live headlines are above."}
          </div>
        </div>
      )}

      {n && <BriefRunDate brief={brief} canRegenerate={isCurrentUserAdmin()} what="grain market" />}
      {n && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 12, color: "#60735f" }}>
            Data as of <strong>{brief.as_of || "—"}</strong> · generated {relTime(brief.generated_at)}
            {brief.model && isCurrentUserAdmin() ? ` · ${brief.model}` : ""}
          </div>

          {n.headline && card(
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#1a2e1a", lineHeight: 1.45, fontFamily: "'Lora','Georgia',serif" }}>
              {n.headline}
            </div>,
            { borderLeft: "4px solid #4a6e4a" }
          )}

          {Array.isArray(n.sections) && n.sections.map((s, i) => card(
            <div key={i}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#2d4a2d", marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 14, color: "#3a4a3a", lineHeight: 1.6 }}>{s.body}</div>
            </div>
          ))}

          {(n.producerContext || n.marketingTakeaway) && card(
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1a4a1a", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>🌾 What this means for a grain producer</div>
              {/* ⚠ THE OLD KEY IS A FALLBACK, NOT A PEER. Briefs stored before
                  2026-09-09 carry marketingTakeaway and nothing else; without
                  this the card blanks on every one of them. New briefs write
                  producerContext — see api/_grain-brief.js for why the KEY had
                  to change and not just the description under it. */}
              <div style={{ fontSize: 14, color: "#1a3a1a", lineHeight: 1.6 }}>{n.producerContext || n.marketingTakeaway}</div>
            </div>,
            { background: "#eaf3ea", border: "1px solid #cfe3cf" }
          )}

          {n.teaching && card(
            <div style={{ fontSize: 13.5, color: "#6a5a2a", lineHeight: 1.6, fontStyle: "italic" }}>
              <strong style={{ fontStyle: "normal" }}>📘 Learn:</strong> {n.teaching}
            </div>,
            { background: "#fbf6e8", border: "1px solid #ece2c4" }
          )}

          {Array.isArray(n.watch) && n.watch.length > 0 && card(
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#2d4a2d", marginBottom: 6 }}>👀 What to watch</div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: "#3a4a3a", lineHeight: 1.7 }}>
                {n.watch.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div>
            <button onClick={() => setShowData(v => !v)} style={{ background: "none", border: "none", color: "#60735f", fontSize: 12, cursor: "pointer", padding: "4px 0", textDecoration: "underline" }}>
              {showData ? "Hide" : "Show"} the data the agent read
            </button>
            {showData && (
              <pre style={{ background: "#1a2e1a", color: "#cfe3cf", padding: 14, borderRadius: 8, fontSize: 11, overflowX: "auto", lineHeight: 1.5, marginTop: 6 }}>
                {JSON.stringify(brief.snapshot, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// LRP PERFORMANCE / MARKET — cattle market context for LRP-Feeder
//
// Data sources (all pre-populated by Phase 1 / Phase 1B sync code):
//   • five_area_weekly        AMS LMPR slug 2477 (LM_CT150) — STEER LIVE FOB Total all grades
//   • boxed_beef_weekly       AMS LMPR slug 2461 (LM_XB459, "Weekly Summary Cutout Values")
//   • nass_data crop=CATTLE   COF_INVENTORY / COF_PLACEMENTS / COF_MARKETINGS  (US 13-state)
//   • nass_data crop=CATTLE   FEEDER_CATTLE_PRICE  (NASS PRICE PAID, monthly national $/cwt)
//   • lrp_endorsements        Filter to Feeder Cattle for the book-coverage card
//
// Footnote labelling: feeder is NASS PRICE PAID (auction-aggregated, monthly).
// Live/intraday CME Feeder Cattle Index integration is deferred until a paid
// data feed is in place — scraping CME is a TOS gray zone. See CLAUDE.md
// "Live/intraday commodity pricing" follow-up.
// ─────────────────────────────────────────────────────────────────────────────
// Fund-positioning history chart: net managed-money position over time with
// labeled axes — dates on X, contracts on the left Y (with a zero line) — plus an
// optional cash-price overlay on an independent right axis so you can eyeball how
// fund length tracks price. Descriptive only: no thresholds or signals drawn.
function CotPositionChart({ history, priceSeries, priceLabel, showPrice, netColor = "#4a6e4a", priceColor = "#1a4a7a", isMobile }) {
  const pts = (history || [])
    .filter((p) => p && p.net != null)
    .map((p) => ({ ts: Date.parse(String(p.d) + "T00:00:00Z"), net: Number(p.net) }))
    .filter((p) => !isNaN(p.ts) && !isNaN(p.net));
  if (pts.length < 2) {
    return <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", color: "#b3c0b0", fontSize: 11, fontStyle: "italic" }}>Not enough history yet</div>;
  }
  const W = isMobile ? 340 : 560, H = 172;
  const showP = showPrice && priceSeries && priceSeries.length > 1;
  const padL = 46, padR = showP ? 50 : 12, padT = 14, padB = 26;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const minTs = pts[0].ts, maxTs = pts[pts.length - 1].ts;

  // net-position Y scale (always include zero)
  let nMin = Math.min(0, ...pts.map((p) => p.net));
  let nMax = Math.max(0, ...pts.map((p) => p.net));
  const nPad = (nMax - nMin) * 0.08 || 1; nMin -= nPad; nMax += nPad;

  // price series clipped to the net-position time window; independent scale
  const pr = showP ? priceSeries.filter((p) => p.ts >= minTs && p.ts <= maxTs && !isNaN(p.value)) : [];
  const pMin = pr.length ? Math.min(...pr.map((p) => p.value)) : 0;
  const pMax = pr.length ? Math.max(...pr.map((p) => p.value)) : 1;
  const pPad = (pMax - pMin) * 0.12 || 1;
  const pLo = pMin - pPad, pHi = pMax + pPad;

  const x = (ts) => padL + ((ts - minTs) / (maxTs - minTs || 1)) * plotW;
  const yN = (v) => padT + plotH - ((v - nMin) / (nMax - nMin || 1)) * plotH;
  const yP = (v) => padT + plotH - ((v - pLo) / (pHi - pLo || 1)) * plotH;
  const kfmt = (v) => (Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + "k" : String(Math.round(v)));

  const netPath = pts.map((p, i) => `${i ? "L" : "M"}${x(p.ts).toFixed(1)},${yN(p.net).toFixed(1)}`).join(" ");
  const pricePath = pr.length ? pr.map((p, i) => `${i ? "L" : "M"}${x(p.ts).toFixed(1)},${yP(p.value).toFixed(1)}`).join(" ") : "";

  const NT = 4;
  const nTicks = []; for (let i = 0; i <= NT; i++) nTicks.push(nMin + (nMax - nMin) * (i / NT));
  const pTicks = []; if (pr.length) for (let i = 0; i <= NT; i++) pTicks.push(pLo + (pHi - pLo) * (i / NT));

  // X ticks — month starts (every 2nd month when the span is long)
  const xTicks = [];
  const spanMs = maxTs - minTs, step = spanMs > 400 * 864e5 ? 2 : 1;
  const s0 = new Date(minTs);
  let d = Date.UTC(s0.getUTCFullYear(), s0.getUTCMonth(), 1);
  while (d <= maxTs) { if (d >= minTs) xTicks.push(d); const dt = new Date(d); d = Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + step, 1); }

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
        {/* net-position grid + left ticks */}
        {nTicks.map((v, i) => (
          <g key={`ny-${i}`}>
            <line x1={padL} x2={W - padR} y1={yN(v)} y2={yN(v)} stroke="#eef2ee" strokeWidth="1" />
            <text x={padL - 5} y={yN(v) + 3} textAnchor="end" fontSize="8.5" fill="#677167" fontFamily="monospace">{kfmt(v)}</text>
          </g>
        ))}
        {/* zero line */}
        {nMin < 0 && nMax > 0 && <line x1={padL} x2={W - padR} y1={yN(0)} y2={yN(0)} stroke="#c0cbba" strokeWidth="1" strokeDasharray="3 3" />}
        {/* price right-axis ticks */}
        {pr.length > 0 && pTicks.map((v, i) => (
          <text key={`py-${i}`} x={W - padR + 5} y={yP(v) + 3} textAnchor="start" fontSize="8.5" fill={priceColor} fontFamily="monospace" opacity="0.8">${v.toFixed(0)}</text>
        ))}
        {/* axis unit labels */}
        <text x={padL - 5} y={padT - 4} textAnchor="end" fontSize="8" fill="#677167">contracts</text>
        {pr.length > 0 && <text x={W - padR + 5} y={padT - 4} textAnchor="start" fontSize="8" fill={priceColor} opacity="0.85">$/cwt</text>}
        {/* X date ticks */}
        {xTicks.filter((t) => t >= minTs && t <= maxTs).map((t, i) => {
          const dt = new Date(t);
          const lbl = dt.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }) + (dt.getUTCMonth() === 0 ? " '" + String(dt.getUTCFullYear()).slice(2) : "");
          return (
            <g key={`x-${i}`}>
              <line x1={x(t)} x2={x(t)} y1={H - padB} y2={H - padB + 3} stroke="#c0cbba" />
              <text x={x(t)} y={H - 9} textAnchor="middle" fontSize="8.5" fill="#60735f">{lbl}</text>
            </g>
          );
        })}
        {/* price line (under net position) */}
        {pricePath && <path d={pricePath} fill="none" stroke={priceColor} strokeWidth="1.6" strokeOpacity="0.8" strokeDasharray="4 3" />}
        {/* net-position line + latest marker */}
        <path d={netPath} fill="none" stroke={netColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(pts[pts.length - 1].ts)} cy={yN(pts[pts.length - 1].net)} r="3" fill={netColor} stroke="#fff" strokeWidth="1.3" />
      </svg>
      <div style={{ display: "flex", gap: 14, marginTop: 2, fontSize: 10.5, color: "#5a7a5a", paddingLeft: padL, flexWrap: "wrap" }}>
        <span><span style={{ display: "inline-block", width: 14, height: 2, background: netColor, verticalAlign: "middle", marginRight: 4 }} />Fund net position (contracts)</span>
        {pr.length > 0 && <span><span style={{ display: "inline-block", width: 14, height: 0, borderTop: `2px dashed ${priceColor}`, verticalAlign: "middle", marginRight: 4 }} />{priceLabel || "Cash price ($/cwt)"}</span>}
      </div>
    </div>
  );
}

function LRPPerformanceView({ farmers, isMobile, mode = "admin" }) {
  // Strictly a market-information page: national cattle benchmarks only (feeder
  // price, fed cattle, boxed beef, cattle-on-feed). No book/agency coverage
  // figures live here — those belong on the Policies tab.
  const [feeder, setFeeder] = useState([]);
  const [fiveArea, setFiveArea] = useState([]);
  const [boxedBeef, setBoxedBeef] = useState([]);
  const [cofRows, setCofRows] = useState([]);
  const [nyFeeder, setNyFeeder] = useState([]);
  const [cattleFut, setCattleFut] = useState(null); // CME live+feeder futures (USDA-published)
  const [cot, setCot] = useState(null); // CFTC managed-money positioning board (cot_positioning)
  const [showCotPrice, setShowCotPrice] = useState(false); // overlay cash price on fund-position chart
  const [timeRange, setTimeRange] = useState("180d"); // "180d" | "1y" | "3y" | "5y" | "all"
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/market?fn=futures")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.commodities) setCattleFut(d); })
      .catch(() => {});
    fetch("/api/market?fn=cot-ag")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setCot(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [fa, bb, cof, fp, ny] = await Promise.all([
          sbFetch("five_area_weekly", {
            select: "report_date,weighted_avg_price,head_count",
            filters: [
              "class_description=eq.STEER",
              "selling_basis_description=eq.LIVE FOB",
              "grade_description=eq.Total all grades",
            ],
            order: "report_date.asc,id.asc",
          }).catch(() => []),
          sbFetch("boxed_beef_weekly", {
            select: "report_date,choice_cutout,select_cutout,total_load",
            order: "report_date.asc,id.asc",
          }).catch(() => []),
          sbFetch("nass_data", {
            select: "year,week,stat_type,value",
            filters: [
              "crop=eq.CATTLE",
              "state=eq.US",
              "stat_type=in.(COF_INVENTORY,COF_PLACEMENTS,COF_MARKETINGS)",
            ],
            order: "year.asc,week.asc,id.asc",
          }).catch(() => []),
          sbFetch("nass_data", {
            select: "year,week,value",
            filters: [
              "crop=eq.CATTLE",
              "state=eq.US",
              "stat_type=eq.FEEDER_CATTLE_PRICE",
            ],
            order: "year.asc,week.asc,id.asc",
          }).catch(() => []),
          sbFetch("feeder_weekly", {
            select: "series,week_ending,avg_price,head_count",
            filters: ["region=eq.NY"],
            order: "week_ending.asc,id.asc",
          }).catch(() => []),
        ]);
        setFiveArea(fa || []);
        setBoxedBeef(bb || []);
        setCofRows(cof || []);
        setFeeder(fp || []);
        setNyFeeder(ny || []);
      } catch (e) { console.error("LRP performance load error:", e); }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#60735f" }}>Loading market data...</div>;

  // ── PARSING & FILTERING ──────────────────────────────────────────────────
  const monthToDate = (year, month) => Date.UTC(year, (month || 1) - 1, 1);
  const now = Date.now();
  const rangeMs = (() => {
    switch (timeRange) {
      case "180d": return 180 * 24 * 60 * 60 * 1000;
      case "1y":   return 365 * 24 * 60 * 60 * 1000;
      case "3y":   return 3 * 365 * 24 * 60 * 60 * 1000;
      case "5y":   return 5 * 365 * 24 * 60 * 60 * 1000;
      default:     return Infinity;
    }
  })();
  const cutoffMs = now - rangeMs;

  // Feeder: monthly. Approx report date = first of month.
  const feederSeries = feeder
    .map(r => ({ ts: monthToDate(r.year, r.week), value: Number(r.value) }))
    .filter(p => !isNaN(p.value) && p.value > 0)
    .sort((a, b) => a.ts - b.ts);
  const feederFiltered = feederSeries.filter(p => p.ts >= cutoffMs);

  // 5-Area: weekly.
  const fiveAreaSeries = fiveArea
    .map(r => ({ ts: new Date(r.report_date).getTime(), value: Number(r.weighted_avg_price), head: Number(r.head_count) }))
    .filter(p => !isNaN(p.value) && p.value > 0)
    .sort((a, b) => a.ts - b.ts);
  const fiveAreaFiltered = fiveAreaSeries.filter(p => p.ts >= cutoffMs);

  // Boxed beef: weekly. Choice + select on same axes.
  const boxedSeries = boxedBeef
    .map(r => ({ ts: new Date(r.report_date).getTime(), choice: Number(r.choice_cutout), select: Number(r.select_cutout), load: r.total_load }))
    .filter(p => !isNaN(p.choice) && p.choice > 0)
    .sort((a, b) => a.ts - b.ts);
  const boxedFiltered = boxedSeries.filter(p => p.ts >= cutoffMs);

  // COF: monthly per stat_type.
  const cofByType = { COF_INVENTORY: [], COF_PLACEMENTS: [], COF_MARKETINGS: [] };
  cofRows.forEach(r => {
    const list = cofByType[r.stat_type];
    if (!list) return;
    list.push({ ts: monthToDate(r.year, r.week), year: r.year, month: r.week, value: Number(r.value) });
  });
  Object.keys(cofByType).forEach(k => cofByType[k].sort((a, b) => a.ts - b.ts));

  // ── KPI VALUES ───────────────────────────────────────────────────────────
  const last = (arr) => arr.length > 0 ? arr[arr.length - 1] : null;
  const prev = (arr) => arr.length > 1 ? arr[arr.length - 2] : null;
  const yearAgo = (arr) => {
    const latest = last(arr);
    if (!latest) return null;
    const target = latest.ts - 365 * 24 * 60 * 60 * 1000;
    let best = null, bestDiff = Infinity;
    for (const p of arr) {
      const d = Math.abs(p.ts - target);
      if (d < bestDiff && d < 45 * 24 * 60 * 60 * 1000) { bestDiff = d; best = p; }
    }
    return best;
  };

  const feederLatest = last(feederSeries);
  const feederPrev   = prev(feederSeries);
  const feederMoM    = feederLatest && feederPrev ? feederLatest.value - feederPrev.value : null;

  const fiveLatest = last(fiveAreaSeries);
  const fivePrev   = prev(fiveAreaSeries);
  const fiveWoW    = fiveLatest && fivePrev ? fiveLatest.value - fivePrev.value : null;

  const boxedLatest = last(boxedSeries);
  const boxedPrev   = prev(boxedSeries);
  const choiceWoW   = boxedLatest && boxedPrev ? boxedLatest.choice - boxedPrev.choice : null;

  const invSeries = cofByType.COF_INVENTORY;
  const invLatest = last(invSeries);
  const invPrev   = prev(invSeries);
  const invMoMPct = invLatest && invPrev ? (invLatest.value - invPrev.value) / invPrev.value : null;
  const invYA     = yearAgo(invSeries);
  const invYoYPct = invLatest && invYA ? (invLatest.value - invYA.value) / invYA.value : null;

  // ── NY FEEDER (USDA AMS NY Weekly Cattle Auction, Albany office) ───────────
  // LOCAL auction cash prices by feeder-steer weight band. NOT the CME Feeder
  // Cattle Index that LRP-Feeder settles on — a regional reference only.
  const NY_BAND_META = [
    { key: "STEER_4_5", label: "4–5 cwt", color: "#8e44ad" },
    { key: "STEER_5_6", label: "5–6 cwt", color: "#c0392b" },
    { key: "STEER_6_7", label: "6–7 cwt", color: "#b85c1a" },
    { key: "STEER_7_8", label: "7–8 cwt", color: "#1a7a4a" },
    { key: "STEER_8_9", label: "8–9 cwt", color: "#1a4a7a" },
  ];
  const nyBandSeries = {};
  for (const b of NY_BAND_META) {
    nyBandSeries[b.key] = nyFeeder
      .filter(r => r.series === b.key)
      .map(r => ({ ts: new Date(r.week_ending).getTime(), value: Number(r.avg_price), head: Number(r.head_count) }))
      .filter(p => !isNaN(p.value) && p.value > 0)
      .sort((a, b) => a.ts - b.ts);
  }
  const nyAllSeries = nyFeeder
    .filter(r => r.series === "STEER_ALL")
    .map(r => ({ ts: new Date(r.week_ending).getTime(), value: Number(r.avg_price), head: Number(r.head_count) }))
    .filter(p => !isNaN(p.value) && p.value > 0)
    .sort((a, b) => a.ts - b.ts);
  const nyAllLatest = last(nyAllSeries);
  const nyAllPrev   = prev(nyAllSeries);
  const nyAllWoW    = nyAllLatest && nyAllPrev ? nyAllLatest.value - nyAllPrev.value : null;
  // latest cash price per weight band (for the by-weight breakout card)
  const nyBandLatest = {};
  for (const b of NY_BAND_META) nyBandLatest[b.key] = last(nyBandSeries[b.key] || []);
  const nyBandWeek = nyBandLatest["STEER_5_6"]?.ts || nyBandLatest["STEER_7_8"]?.ts || nyAllLatest?.ts || null;

  // ── DATA FRESHNESS ────────────────────────────────────────────────────────
  // Latest data point per source — derived from already-loaded series, no
  // extra queries. If a cron silently fails (Vercel error, AMS/NASS API down),
  // the latest-data date stops advancing and the staleness dot turns red.
  // Thresholds: "okMaxDays" = expected max staleness for a healthy sync;
  // beyond 2× that = red. Weekly sources (5-Area, Boxed Beef) tolerate up to
  // ~14 days (cron Tuesday + week buffer); monthly sources (COF, Feeder) up
  // to ~35 days (publish 3rd Friday + cron 22nd + buffer).
  const sources = [
    { key: "fiveArea",  icon: "🥩", label: "5-Area",     ts: fiveAreaSeries.length > 0 ? fiveAreaSeries[fiveAreaSeries.length - 1].ts : null, okMaxDays: 14 },
    { key: "boxedBeef", icon: "📦", label: "Boxed Beef", ts: boxedSeries.length > 0 ? boxedSeries[boxedSeries.length - 1].ts : null, okMaxDays: 14 },
    { key: "cof",       icon: "🏭", label: "COF",        ts: cofByType.COF_INVENTORY.length > 0 ? cofByType.COF_INVENTORY[cofByType.COF_INVENTORY.length - 1].ts : null, okMaxDays: 40 },
    { key: "feeder",    icon: "🐂", label: "Feeder",     ts: feederSeries.length > 0 ? feederSeries[feederSeries.length - 1].ts : null, okMaxDays: 40 },
    { key: "nyFeeder",  icon: "🐮", label: "NY Auction", ts: nyAllSeries.length > 0 ? nyAllSeries[nyAllSeries.length - 1].ts : null, okMaxDays: 14 },
    { key: "cot",       icon: "💰", label: "CFTC",       ts: (() => { const rd = cot?.live_cattle?.report_date || cot?.feeder_cattle?.report_date; return rd ? Date.parse(rd + "T00:00:00Z") : null; })(), okMaxDays: 13 },
  ];

  // ── FORMATTERS ────────────────────────────────────────────────────────────
  const fmt$ = (v, d=2) => v == null ? "—" : "$" + Number(v).toFixed(d);
  const fmtN = (v) => v == null ? "—" : Math.round(Number(v)).toLocaleString();
  const fmtNshort = (v) => {
    if (v == null) return "—";
    if (v >= 1e6) return (v/1e6).toFixed(2) + "M";
    if (v >= 1000) return Math.round(v/1000) + "K";
    return Math.round(v).toLocaleString();
  };
  const fmtDeltaCash = (v, d=2) => {
    if (v == null) return "—";
    return (v >= 0 ? "+$" : "−$") + Math.abs(v).toFixed(d);
  };
  const fmtDeltaPct = (v) => {
    if (v == null) return "—";
    return (v >= 0 ? "+" : "−") + (Math.abs(v) * 100).toFixed(1) + "%";
  };
  // All timestamps are stored as UTC midnight. Render them with timeZone:"UTC"
  // so EDT/EST users don't see the date shifted backward by one day.
  const fmtDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit", timeZone: "UTC" });
  };
  const fmtMonthYear = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
  };
  const deltaColor = (v) => v == null ? "#677167" : v >= 0 ? "#1a7a1a" : "#c0392b";

  // ── KPI CARD ──────────────────────────────────────────────────────────────
  const KpiCard = ({ icon, label, value, subline, deltaText, deltaVal, footnote }) => (
    <div style={{
      flex: "1 1 200px",
      padding: isMobile ? "12px 14px" : "14px 18px",
      background: "#fff",
      border: "1px solid #d4ddd4",
      borderRadius: 10,
      minWidth: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#5a7a5a", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      </div>
      <div style={{ fontSize: isMobile ? 19 : 22, fontWeight: 800, color: "#1a2e1a", fontVariantNumeric: "tabular-nums", marginBottom: 2 }}>
        {value}
      </div>
      {subline && <div style={{ fontSize: 11, color: "#60735f", marginBottom: 4 }}>{subline}</div>}
      {deltaText != null && (
        <div style={{ fontSize: 11, fontWeight: 700, color: deltaColor(deltaVal) }}>{deltaText}</div>
      )}
      {footnote && <div style={{ fontSize: 10, color: "#aaa", marginTop: 4, fontStyle: "italic" }}>{footnote}</div>}
    </div>
  );

  // ── LINE CHART ────────────────────────────────────────────────────────────
  // series: [{ts, value}, ...] sorted by ts. seriesB optional (overlay).
  const LineChart = ({ series, seriesB, height = 200, color = "#1a2e1a", colorB = "#c0392b", labelA, labelB, yLabel, fmtY }) => {
    const W = isMobile ? 340 : 720;
    const H = height;
    const padL = 56, padR = 16, padT = 16, padB = 30;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    const allPts = [...(series || []), ...(seriesB || [])].filter(p => p && !isNaN(p.value));
    if (allPts.length === 0) {
      return <div style={{ padding: 30, textAlign: "center", color: "#aaa", fontSize: 12 }}>No data in selected range</div>;
    }

    const minTs = Math.min(...allPts.map(p => p.ts));
    const maxTs = Math.max(...allPts.map(p => p.ts));
    const minVal = Math.min(...allPts.map(p => p.value));
    const maxVal = Math.max(...allPts.map(p => p.value));
    const yPad = (maxVal - minVal) * 0.1 || 1;
    const yMin = Math.max(0, minVal - yPad);
    const yMax = maxVal + yPad;

    const xScale = (ts) => padL + ((ts - minTs) / (maxTs - minTs || 1)) * plotW;
    const yScale = (v) => padT + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;

    const pathOf = (s) => s.map((p, i) => `${i === 0 ? "M" : "L"}${xScale(p.ts).toFixed(1)},${yScale(p.value).toFixed(1)}`).join(" ");

    // Y-axis ticks
    const yTicks = [];
    const tickCount = 5;
    for (let i = 0; i <= tickCount; i++) {
      yTicks.push(yMin + (yMax - yMin) * (i / tickCount));
    }
    const formatY = fmtY || ((v) => v.toFixed(0));

    // X-axis ticks (month markers)
    const dayMs = 24 * 60 * 60 * 1000;
    const spanDays = (maxTs - minTs) / dayMs;
    const xTicks = [];
    if (spanDays <= 200) {
      // Monthly ticks
      const start = new Date(minTs);
      let d = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1);
      while (d <= maxTs) {
        if (d >= minTs) xTicks.push(d);
        const dt = new Date(d);
        d = Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 1);
      }
    } else if (spanDays <= 365 * 2) {
      // Quarterly
      const start = new Date(minTs);
      let d = Date.UTC(start.getUTCFullYear(), Math.floor(start.getUTCMonth() / 3) * 3, 1);
      while (d <= maxTs) {
        if (d >= minTs) xTicks.push(d);
        const dt = new Date(d);
        d = Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 3, 1);
      }
    } else {
      // Yearly
      const startYr = new Date(minTs).getUTCFullYear();
      const endYr = new Date(maxTs).getUTCFullYear();
      for (let y = startYr; y <= endYr; y++) xTicks.push(Date.UTC(y, 0, 1));
    }

    return (
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
          {/* Y grid + ticks */}
          {yTicks.map((v, i) => (
            <g key={`y-${i}`}>
              <line x1={padL} x2={W - padR} y1={yScale(v)} y2={yScale(v)} stroke="#eef2ee" strokeWidth="1" />
              <text x={padL - 6} y={yScale(v) + 4} textAnchor="end" fontSize="10" fill="#677167">{formatY(v)}</text>
            </g>
          ))}
          {yLabel && <text x={padL - 8} y={padT - 4} textAnchor="end" fontSize="9" fill="#aaa">{yLabel}</text>}
          {/* X tick labels — UTC-anchored so timezone shift doesn't push month boundaries */}
          {xTicks.filter(t => t >= minTs && t <= maxTs).map((t, i) => {
            const dt = new Date(t);
            const monLbl = dt.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
            const lbl = spanDays <= 200
              ? monLbl
              : spanDays <= 365 * 2
                ? `${monLbl} '${String(dt.getUTCFullYear()).slice(2)}`
                : `'${String(dt.getUTCFullYear()).slice(2)}`;
            return (
              <text key={`x-${i}`} x={xScale(t)} y={H - 10} textAnchor="middle" fontSize="9" fill="#60735f">{lbl}</text>
            );
          })}
          {/* Series A */}
          {series && series.length > 0 && <path d={pathOf(series)} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
          {/* Series B (overlay) */}
          {seriesB && seriesB.length > 0 && <path d={pathOf(seriesB)} fill="none" stroke={colorB} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="0" />}
          {/* Latest value markers */}
          {series && series.length > 0 && (() => {
            const p = series[series.length - 1];
            return <circle cx={xScale(p.ts)} cy={yScale(p.value)} r="3.5" fill={color} stroke="#fff" strokeWidth="1.5" />;
          })()}
          {seriesB && seriesB.length > 0 && (() => {
            const p = seriesB[seriesB.length - 1];
            return <circle cx={xScale(p.ts)} cy={yScale(p.value)} r="3.5" fill={colorB} stroke="#fff" strokeWidth="1.5" />;
          })()}
        </svg>
        {(labelA || labelB) && (
          <div style={{ display: "flex", gap: 16, marginTop: 4, fontSize: 11, color: "#5a7a5a", paddingLeft: padL }}>
            {labelA && <span><span style={{ display: "inline-block", width: 14, height: 2, background: color, verticalAlign: "middle", marginRight: 5 }} />{labelA}</span>}
            {labelB && <span><span style={{ display: "inline-block", width: 14, height: 2, background: colorB, verticalAlign: "middle", marginRight: 5 }} />{labelB}</span>}
          </div>
        )}
      </div>
    );
  };

  // ── BAR CHART (small, for COF 24-month series) ───────────────────────────
  const BarChart = ({ series, height = 100, color = "#4a6e4a", fmtY }) => {
    const W = isMobile ? 320 : 400;
    const H = height;
    const padL = 50, padR = 8, padT = 10, padB = 22;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const points = series.slice(-24); // last 24 months
    if (points.length === 0) {
      return <div style={{ padding: 16, textAlign: "center", color: "#aaa", fontSize: 11 }}>No data</div>;
    }
    const maxV = Math.max(...points.map(p => p.value));
    const minV = Math.min(...points.map(p => p.value));
    const yPad = (maxV - minV) * 0.1 || 1;
    const yMin = Math.max(0, minV - yPad);
    const yMax = maxV + yPad;
    const yScale = (v) => padT + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;
    const barW = plotW / points.length * 0.8;
    const formatY = fmtY || ((v) => fmtNshort(v));

    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
        {/* Y ticks: just min and max */}
        <line x1={padL} x2={W - padR} y1={yScale(yMin)} y2={yScale(yMin)} stroke="#eef2ee" />
        <line x1={padL} x2={W - padR} y1={yScale(yMax)} y2={yScale(yMax)} stroke="#eef2ee" />
        <text x={padL - 4} y={yScale(yMin) + 3} textAnchor="end" fontSize="9" fill="#677167">{formatY(yMin)}</text>
        <text x={padL - 4} y={yScale(yMax) + 3} textAnchor="end" fontSize="9" fill="#677167">{formatY(yMax)}</text>
        {/* Bars */}
        {points.map((p, i) => {
          const x = padL + (i + 0.1) * (plotW / points.length);
          const y = yScale(p.value);
          const h = yScale(yMin) - y;
          const isLast = i === points.length - 1;
          return <rect key={i} x={x} y={y} width={barW} height={h} fill={isLast ? "#1a2e1a" : color} opacity={isLast ? 1 : 0.75} />;
        })}
        {/* X labels (sparse) */}
        {points.map((p, i) => {
          if (i % 6 !== 0 && i !== points.length - 1) return null;
          const x = padL + (i + 0.5) * (plotW / points.length);
          const lbl = `${String(p.year).slice(2)}'${String(p.month).padStart(2, "0")}`;
          return <text key={`x-${i}`} x={x} y={H - 7} textAnchor="middle" fontSize="9" fill="#60735f">{lbl}</text>;
        })}
      </svg>
    );
  };

  // ── REFERENCE TABLE — last 12 weeks of price data ────────────────────────
  // Boxed beef publishes Fridays; 5-Area publishes Mondays for the prior week's
  // trade. Same week, different report_date — naive equality join leaves 5-Area
  // null for every row. Bucket by nearest-within-7-days using boxed beef Friday
  // as the canonical week-end date.
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const findNearestFiveArea = (boxedTs) => {
    let best = null, bestDiff = Infinity;
    for (const p of fiveAreaSeries) {
      const diff = Math.abs(p.ts - boxedTs);
      if (diff < bestDiff && diff <= SEVEN_DAYS_MS) { bestDiff = diff; best = p; }
    }
    return best ? best.value : null;
  };
  const last12Weeks = (() => {
    const dates = [...new Set(boxedSeries.map(p => p.ts))].sort((a, b) => b - a).slice(0, 12);
    const boxedByDate = Object.fromEntries(boxedSeries.map(p => [p.ts, p]));
    return dates.map(ts => ({
      ts,
      fiveArea: findNearestFiveArea(ts),
      choice: boxedByDate[ts]?.choice || null,
      select: boxedByDate[ts]?.select || null,
    }));
  })();

  const last12Months = invSeries.slice(-12).reverse().map(p => ({
    ts: p.ts,
    inventory: p.value,
    placements: cofByType.COF_PLACEMENTS.find(x => x.year === p.year && x.month === p.month)?.value || null,
    marketings: cofByType.COF_MARKETINGS.find(x => x.year === p.year && x.month === p.month)?.value || null,
  }));

  // CSV export — both tables in one file
  const exportCSV = () => {
    const lines = [];
    lines.push("LRP Performance / Market — export " + new Date().toISOString().slice(0, 10));
    lines.push("");
    lines.push("WEEKLY PRICES (last 12 weeks)");
    lines.push("report_date,5_Area_STEER_LIVE_FOB_$/cwt,Choice_Cutout_$/cwt,Select_Cutout_$/cwt");
    last12Weeks.forEach(r => {
      lines.push([new Date(r.ts).toISOString().slice(0,10), r.fiveArea || "", r.choice || "", r.select || ""].join(","));
    });
    lines.push("");
    lines.push("CATTLE ON FEED (last 12 months, US 13-state)");
    lines.push("month,Inventory_head,Placements_head,Marketings_head");
    last12Months.forEach(r => {
      lines.push([new Date(r.ts).toISOString().slice(0,7), r.inventory || "", r.placements || "", r.marketings || ""].join(","));
    });
    lines.push("");
    lines.push("FEEDER CATTLE PRICE (last 12 months, NASS PRICE PAID, $/cwt)");
    lines.push("month,Price");
    feederSeries.slice(-12).reverse().forEach(p => {
      lines.push([new Date(p.ts).toISOString().slice(0,7), p.value].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `lrp_market_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── RENDER ───────────────────────────────────────────────────────────────
  const sectionTitle = { fontSize: 13, fontWeight: 700, color: "#1a2e1a", marginBottom: 8, fontFamily: "'Lora','Georgia',serif" };
  const cardWrap = { background: "#fff", border: "1px solid #e0eae0", borderRadius: 10, padding: isMobile ? "14px 14px" : "16px 18px", marginBottom: 14 };

  // Cash-price series for the fund-position overlay (both already loaded above).
  // Fed → 5-Area national cash; feeder → NY 7–8cwt cash (regional proxy; the CME
  // feeder index isn't stored historically). Clearly labeled as cash on the chart.
  const cotPrice = {
    live_cattle: (fiveArea || []).map((r) => ({ ts: Date.parse(r.report_date + "T00:00:00Z"), value: Number(r.weighted_avg_price) })).filter((p) => !isNaN(p.ts) && !isNaN(p.value)),
    feeder_cattle: (nyFeeder || []).filter((r) => r.series === "STEER_7_8").map((r) => ({ ts: Date.parse(r.week_ending + "T00:00:00Z"), value: Number(r.avg_price) })).filter((p) => !isNaN(p.ts) && !isNaN(p.value)),
  };
  const cotPriceLabel = { live_cattle: "5-Area fed cash ($/cwt)", feeder_cattle: "NY feeder 7–8cwt cash ($/cwt)" };

  const rangeBtn = (key, label) => (
    <button key={key} onClick={() => setTimeRange(key)} style={{
      padding: "5px 11px", borderRadius: 14, fontSize: 11, fontWeight: 600,
      border: timeRange === key ? "2px solid #4a6e4a" : "1px solid #d0d8c8",
      background: timeRange === key ? "#4a6e4a" : "#fff",
      color: timeRange === key ? "#fff" : "#5a7a5a",
      cursor: "pointer",
    }}>{label}</button>
  );

  return (
    <div style={{ paddingTop: 14 }}>
      {/* Data freshness — colored dots flag silent cron failures */}
      <div style={{ display: "flex", gap: isMobile ? 8 : 14, fontSize: 10, color: "#60735f", marginBottom: 10, flexWrap: "wrap" }}>
        {sources.map(s => {
          const ageDays = s.ts != null ? (now - s.ts) / (24 * 60 * 60 * 1000) : null;
          const dotColor = ageDays == null ? "#ccc"
            : ageDays > s.okMaxDays * 2 ? "#c0392b"
            : ageDays > s.okMaxDays     ? "#b07820"
            : "#1a7a1a";
          return (
            <span key={s.key} title={`${s.label} latest data point · OK if within ${s.okMaxDays}d`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: dotColor, display: "inline-block" }} />
              <span>{s.icon}</span>
              <span style={{ fontWeight: 600 }}>{s.label}:</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{s.ts ? fmtDate(s.ts) : "—"}</span>
            </span>
          );
        })}
      </div>

      {/* Top controls: time range + CSV export */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {rangeBtn("180d", "180d")}
          {rangeBtn("1y", "1Y")}
          {rangeBtn("3y", "3Y")}
          {rangeBtn("5y", "5Y")}
          {rangeBtn("all", "All")}
        </div>
        <button onClick={exportCSV} style={{
          padding: "6px 14px", borderRadius: 8, border: "1px solid #d0e0d0",
          background: "#f8faf8", color: "#4a6e4a", fontSize: 12, fontWeight: 700, cursor: "pointer",
        }}>📥 Export CSV</button>
      </div>

      {/* CME cattle futures — the DAILY-updated settlement-index contracts (from
          USDA's National Daily Feeder & Stocker report), the prices LRP-Feeder /
          LRP-Fed actually settle against. Pinned to the top; cash/NASS benchmarks below. */}
      {cattleFut && (cattleFut.commodities?.live_cattle?.length || cattleFut.commodities?.feeder_cattle?.length) && (
        <div style={cardWrap}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
            <div style={sectionTitle}>🐄 CME Cattle Futures — daily settlements (LRP settles here)</div>
            <span style={{ fontSize: 10, color: "#2d6a1a", fontWeight: 700 }}>● Live · USDA settled {cattleFut.cattle_as_of}</span>
          </div>
          <div style={{ fontSize: 11, color: "#60735f", marginBottom: 10 }}>
            Nearby CME contracts from USDA's National Daily Feeder & Stocker Summary — the board LRP-Feeder / LRP-Fed actually settle against ($/cwt).
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            {[["live_cattle", "🥩 Live Cattle (Fed)"], ["feeder_cattle", "🐂 Feeder Cattle"]].map(([key, label]) => {
              const rows = cattleFut.commodities?.[key] || [];
              const front = rows[0];
              return (
                <div key={key} style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a" }}>{label}</span>
                    {front && <span style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>${Number(front.settle_price).toFixed(3)}</span>}
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.contract_month} style={{ borderTop: i ? "1px solid #f0f4ee" : "none" }}>
                          <td style={{ padding: "4px 4px", color: "#60735f" }}>{r.contract_month}</td>
                          <td style={{ padding: "4px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>${Number(r.settle_price).toFixed(3)}</td>
                          <td style={{ padding: "4px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 11, color: r.settle_change == null ? "#aaa" : r.settle_change >= 0 ? "#2d6a1a" : "#c0392b" }}>
                            {r.settle_change == null ? "—" : (r.settle_change >= 0 ? "+" : "") + Number(r.settle_change).toFixed(3)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fund positioning (CFTC managed money) — descriptive only. Net long/short,
          strength vs recent range, momentum, and the net-position history with an
          optional cash-price overlay. No signals, no thresholds, no buy/sell — just
          what the large speculators are doing on the contracts LRP settles against. */}
      {cot && (cot.live_cattle || cot.feeder_cattle) && (
        <div style={cardWrap}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={sectionTitle}>💰 Fund Positioning — managed money (CFTC)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={() => setShowCotPrice((v) => !v)} style={{
                padding: "4px 11px", borderRadius: 14, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                border: showCotPrice ? "2px solid #1a4a7a" : "1px solid #c8d8c0",
                background: showCotPrice ? "#1a4a7a" : "#fff", color: showCotPrice ? "#fff" : "#4a6e4a",
              }}>{showCotPrice ? "✓ Price overlay" : "＋ Overlay price"}</button>
              <span style={{ fontSize: 10, color: "#60735f", fontWeight: 700 }}>CFTC {(cot.live_cattle || cot.feeder_cattle)?.report_date}</span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#60735f", marginBottom: 10 }}>
            Net position of managed money (large speculators) on the CME — long minus short contracts — and how it's trending. Higher = funds more net-long; lower = more net-short. Weekly CFTC data, ~1-week lag.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            {[["live_cattle", "🥩 Live Cattle (Fed)"], ["feeder_cattle", "🐂 Feeder Cattle"]].map(([key, label]) => {
              const c = cot[key];
              if (!c) return (
                <div key={key} style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>{label}</div>
                  <div style={{ color: "#aaa", fontSize: 12 }}>Positioning unavailable.</div>
                </div>
              );
              const hist = c.history || [];
              const nets = hist.map((h) => h.net).filter((v) => v != null);
              const wow = nets.length >= 2 ? nets[nets.length - 1] - nets[nets.length - 2] : null;
              const chg4 = nets.length >= 5 ? nets[nets.length - 1] - nets[nets.length - 5] : null;
              // 4-week momentum: is net position rising or falling? (small deadband so noise reads "little changed")
              const momoUp = chg4 != null && chg4 > 500;
              const momoDown = chg4 != null && chg4 < -500;
              const momoDesc = chg4 == null ? null : momoUp ? "Net position rising" : momoDown ? "Net position falling" : "Little changed";
              const momoArrow = chg4 == null ? "" : momoUp ? "▲" : momoDown ? "▼" : "▬";
              const momoCol = chg4 == null ? "#60735f" : momoUp ? "#2d6a1a" : momoDown ? "#c0392b" : "#60735f";
              // descriptive strength label from net-as-%-of-open-interest lean
              const oi = c.mm_net_pct_oi;
              const strength = oi == null ? null
                : oi >= 0.30 ? "Heavily net-long" : oi >= 0.10 ? "Net-long"
                : oi > -0.10 ? "Balanced" : oi > -0.30 ? "Net-short" : "Heavily net-short";
              const strengthCol = oi == null ? "#60735f" : oi >= 0.10 ? "#2d6a1a" : oi <= -0.10 ? "#8a5a2a" : "#60735f";
              const chip = (txt, col) => (
                <span style={{ fontSize: 10, fontWeight: 700, color: col, background: col + "18", border: `1px solid ${col}44`, borderRadius: 10, padding: "2px 8px" }}>{txt}</span>
              );
              return (
                <div key={key} style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>{label}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: c.mm_net >= 0 ? "#2d6a1a" : "#c0392b" }}>
                      {c.mm_net >= 0 ? "+" : ""}{Number(c.mm_net).toLocaleString()}
                    </span>
                    <span style={{ fontSize: 11, color: "#60735f" }}>net {c.mm_net >= 0 ? "long" : "short"}</span>
                    {wow != null && (
                      <span style={{ fontSize: 12, fontWeight: 700, color: wow >= 0 ? "#2d6a1a" : "#c0392b" }}>
                        {wow >= 0 ? "▲ +" : "▼ "}{Number(wow).toLocaleString()} wk/wk
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: "#677167", marginTop: 2 }}>
                    {Number(c.mm_long).toLocaleString()} long / {Number(c.mm_short).toLocaleString()} short
                    {oi != null ? ` · ${(oi * 100).toFixed(1)}% of OI` : ""}
                  </div>
                  {/* descriptive strength chip */}
                  {strength && <div style={{ marginTop: 8 }}>{chip(strength, strengthCol)}</div>}
                  {/* momentum box — plain 4-week change in net position */}
                  {chg4 != null && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "#f9fcf6", border: "1px solid #e4ece4", borderRadius: 8, padding: "7px 11px", marginTop: 8 }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#60735f", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Momentum · last 4 wks</div>
                        <div style={{ fontSize: 11, color: "#5a6e5a" }}>{momoDesc}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: momoCol }}>{momoArrow} {chg4 >= 0 ? "+" : ""}{Number(chg4).toLocaleString()}</div>
                        <div style={{ fontSize: 10, color: "#677167" }}>contracts</div>
                      </div>
                    </div>
                  )}
                  {c.wk52_pct != null && (
                    <>
                      <div style={{ position: "relative", height: 9, background: "#eef2ec", borderRadius: 5, margin: "12px 0 4px" }}>
                        <div style={{ position: "absolute", left: `calc(${(c.wk52_pct * 100).toFixed(0)}% - 5px)`, top: -3, width: 10, height: 10, borderRadius: 5, background: "#4a6e4a", border: "2px solid #fff", boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }} />
                      </div>
                      <div style={{ fontSize: 10, color: "#677167", textAlign: "center" }}>
                        {Math.round(c.wk52_pct * 100)}%ile of 52-wk net-position range{c.wk52_min != null && c.wk52_max != null ? ` · ${Number(c.wk52_min).toLocaleString()} to ${Number(c.wk52_max).toLocaleString()}` : ""}
                      </div>
                    </>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <CotPositionChart history={hist} priceSeries={cotPrice[key]} priceLabel={cotPriceLabel[key]} showPrice={showCotPrice} isMobile={isMobile} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI strip */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
        <KpiCard
          icon="🐂" label="Feeder Cattle"
          value={fmt$(feederLatest?.value)}
          subline={feederLatest ? fmtMonthYear(feederLatest.ts) : "—"}
          deltaText={fmtDeltaCash(feederMoM) + " MoM"}
          deltaVal={feederMoM}
          footnote="NASS PRICE PAID · national monthly · not CME index"
        />
        {nyBandWeek && (
          <div style={{ background: "#fff", border: "1px solid #e0eae0", borderRadius: 12, padding: "12px 14px", minWidth: isMobile ? 150 : 200, flex: "1 1 auto" }}>
            <div style={{ fontSize: 11, color: "#60735f", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>🐮 NY Feeder Steers</div>
            <div style={{ fontSize: 10, color: "#9aaa98", marginBottom: 6 }}>by weight · wk {fmtDate(nyBandWeek)} · local cash</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <tbody>
                {NY_BAND_META.map((b) => {
                  const p = nyBandLatest[b.key];
                  return (
                    <tr key={b.key}>
                      <td style={{ padding: "2px 0", color: "#5a6e5a" }}>{b.label}</td>
                      <td style={{ padding: "2px 0", textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "#1a2e1a" }}>{p ? fmt$(p.value) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: "#9aaa98", marginTop: 4 }}>7–8 cwt ≈ CME-index basis · lighter = higher $/cwt</div>
          </div>
        )}
        <KpiCard
          icon="🥩" label="5 Area Fed Cattle"
          value={fmt$(fiveLatest?.value)}
          subline={fiveLatest ? "STEER · Live FOB · " + fmtDate(fiveLatest.ts) : "—"}
          deltaText={fmtDeltaCash(fiveWoW) + " WoW"}
          deltaVal={fiveWoW}
        />
        <KpiCard
          icon="📦" label="Choice Cutout"
          value={fmt$(boxedLatest?.choice)}
          subline={boxedLatest ? "vs Select " + fmt$(boxedLatest.select) + " · " + fmtDate(boxedLatest.ts) : "—"}
          deltaText={fmtDeltaCash(choiceWoW) + " WoW"}
          deltaVal={choiceWoW}
        />
        <KpiCard
          icon="🏭" label="Cattle on Feed"
          value={fmtNshort(invLatest?.value)}
          subline={invLatest ? "head · " + fmtMonthYear(invLatest.ts) : "—"}
          deltaText={fmtDeltaPct(invMoMPct) + " MoM · " + fmtDeltaPct(invYoYPct) + " YoY"}
          deltaVal={invYoYPct}
        />
      </div>


      {/* Charts */}
      <div style={cardWrap}>
        <div style={sectionTitle}>🐂 Feeder Cattle Price (NASS PRICE PAID)</div>
        <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8 }}>
          National monthly · NASS Quick Stats — a broad survey average across all feeder weights, so it runs above the CME Feeder Cattle Index (700–899 lb steers) that LRP-Feeder actually settles on. <em>Directional benchmark, not the settlement index.</em>
        </div>
        <LineChart series={feederFiltered} color="#b85c1a" yLabel="$/cwt" fmtY={(v) => "$" + v.toFixed(0)} labelA="Feeder $/cwt" />
      </div>

      {nyAllSeries.length > 0 && (
        <div style={cardWrap}>
          <div style={sectionTitle}>🐮 New York Feeder Steers — Local Auction</div>
          <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8 }}>
            Weekly · USDA AMS New York Weekly Cattle Auction Summary (Albany office) · head-weighted feeder-steer cash prices by weight band. <em>Local NY auction prices — not the CME settlement index.</em>
          </div>
          <LineChart
            series={nyBandSeries["STEER_5_6"].filter(p => p.ts >= cutoffMs)}
            seriesB={nyBandSeries["STEER_7_8"].filter(p => p.ts >= cutoffMs)}
            color="#c0392b" colorB="#1a4a7a"
            labelA="5–6 cwt" labelB="7–8 cwt"
            yLabel="$/cwt" fmtY={(v) => "$" + v.toFixed(0)}
          />
          <div style={{ fontSize: 10, color: "#9aaa98", marginTop: 6 }}>
            Lighter cattle command higher $/cwt — the 5–6 cwt line sits above 7–8 cwt by design. NY auction volume is thin (~100 head/wk), so weekly points can be choppy.
          </div>
        </div>
      )}

      <div style={cardWrap}>
        <div style={sectionTitle}>🥩 5 Area Fed Cattle (STEER · Live FOB · Total all grades)</div>
        <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8 }}>Weekly · AMS LMPR LM_CT150 · weighted avg of negotiated trade across 5 cattle regions</div>
        <LineChart series={fiveAreaFiltered} color="#1a4a7a" yLabel="$/cwt" fmtY={(v) => "$" + v.toFixed(0)} labelA="5-Area Steer $/cwt" />
      </div>

      <div style={cardWrap}>
        <div style={sectionTitle}>📦 Boxed Beef Cutout — Choice & Select</div>
        <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8 }}>Weekly · AMS LMPR LM_XB459 · 600-900 lb carcass cutout</div>
        <LineChart
          series={boxedFiltered.map(p => ({ ts: p.ts, value: p.choice }))}
          seriesB={boxedFiltered.map(p => ({ ts: p.ts, value: p.select }))}
          color="#7a1a4a" colorB="#1a7a4a"
          labelA="Choice" labelB="Select"
          yLabel="$/cwt" fmtY={(v) => "$" + v.toFixed(0)}
        />
      </div>

      <div style={cardWrap}>
        <div style={sectionTitle}>🏭 Cattle on Feed — 24-Month Trend (US 13-state, ≥1,000 head capacity)</div>
        <div style={{ fontSize: 11, color: "#60735f", marginBottom: 12 }}>Monthly · NASS · supply-side context for fed cattle pipeline</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#5a7a5a", marginBottom: 4 }}>Inventory</div>
            <BarChart series={cofByType.COF_INVENTORY} color="#4a6e4a" />
          </div>
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#5a7a5a", marginBottom: 4 }}>Placements</div>
            <BarChart series={cofByType.COF_PLACEMENTS} color="#7a9258" />
          </div>
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#5a7a5a", marginBottom: 4 }}>Marketings</div>
            <BarChart series={cofByType.COF_MARKETINGS} color="#a87a3a" />
          </div>
        </div>
      </div>

      {/* Reference table — weekly prices */}
      <div style={cardWrap}>
        <div style={sectionTitle}>📊 Last 12 Weeks — Cattle & Boxed Beef Prices</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f4f8f0" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Week ending</th>
                <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>5-Area Steer $/cwt</th>
                <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Choice $/cwt</th>
                <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Select $/cwt</th>
                <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Spread</th>
              </tr>
            </thead>
            <tbody>
              {last12Weeks.map(r => {
                const spread = r.choice != null && r.select != null ? r.choice - r.select : null;
                return (
                  <tr key={r.ts} style={{ borderBottom: "1px solid #f0f4f0" }}>
                    <td style={{ padding: "7px 12px", color: "#1a2e1a" }}>{fmtDate(r.ts)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#1a4a7a" }}>{fmt$(r.fiveArea)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#7a1a4a" }}>{fmt$(r.choice)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#1a7a4a" }}>{fmt$(r.select)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#5a7a5a" }}>{fmt$(spread)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reference table — monthly COF */}
      <div style={cardWrap}>
        <div style={sectionTitle}>📊 Last 12 Months — Cattle on Feed (US 13-state)</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f4f8f0" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Month</th>
                <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Inventory</th>
                <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Placements</th>
                <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, color: "#1a2e1a", borderBottom: "1px solid #e0eae0" }}>Marketings</th>
              </tr>
            </thead>
            <tbody>
              {last12Months.map(r => (
                <tr key={r.ts} style={{ borderBottom: "1px solid #f0f4f0" }}>
                  <td style={{ padding: "7px 12px", color: "#1a2e1a" }}>{fmtMonthYear(r.ts)}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#4a6e4a" }}>{fmtN(r.inventory)}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#7a9258" }}>{fmtN(r.placements)}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#a87a3a" }}>{fmtN(r.marketings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// MAP VIEW — NY County Market Map with Market Share + Growth Opportunity
// ─────────────────────────────────────────────────────────────────────────────
function MapView({ farmers, isMobile }) {
  const [activeLayer, setActiveLayer] = useState('CORN');
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('share');
  // Yield-layer data source: 'book' = agency M13 APH (default), 'rma' = official
  // RMA Final county yields (PM-26-029, NY only) from RMA_COUNTY_EXPECTED_REVENUE.
  const [yieldSource, setYieldSource] = useState('book');
  const [historicalData, setHistoricalData] = useState([]);
  const [d3Lib, setD3Lib] = useState(window.d3 || null);
  const [showSidebar, setShowSidebar] = useState(!isMobile);
  // Market-map data sources (replaced hardcoded RMA_COUNTY_2025/2026 constants 2026-04-24)
  const [sobData, setSobData] = useState([]);              // all rows from sob_data (NY, all crops, CY25+26)
  const [nassData, setNassData] = useState([]);            // ACRES_PLANTED / HARVESTED for row-crops + HAY
  const [lastM13Import, setLastM13Import] = useState(null); // {imported_at, filename, policy_count}

  const NYC_SET = useMemo(() => new Set(["Kings","Queens","Bronx","Richmond","New York"]), []);

  // Load TopoJSON + historical data + d3 + yield history + sob_data + nass_data + m13 log
  const [yieldMapData, setYieldMapData] = useState([]);
  useEffect(() => {
    Promise.all([
      d3Promise,
      fetch('https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json').then(r => r.json()).catch(() => null),
      sbFetch("historical_summaries").catch(() => []),
      sbFetch("production_history", { filters: ['crop_year=eq.2025','crop=in.(CORN,SOYBEANS,WHEAT)','source_file=eq.M13_auto'] }).catch(() => []),
      sbFetch("sob_data", { filters: ['state=eq.NY'] }).catch(() => []),
      sbFetch("nass_data", { filters: ['state=eq.NY', 'stat_type=in.(ACRES_PLANTED,ACRES_HARVESTED)', 'crop=in.(CORN,SOYBEANS,WHEAT,HAY)'] }).catch(() => []),
      sbFetch("m13_import_log", { order: "imported_at.desc,id.asc", filters: ['status=eq.success'] }).catch(() => []),
    ]).then(([d3Loaded, topo, hist, yieldRows, sobRows, nassRows, m13Rows]) => {
      if (d3Loaded) setD3Lib(d3Loaded);
      if (topo) {
        const features = decodeTopo(topo, topo.objects.counties).filter(f => f.properties.fips.startsWith('36'));
        setGeoData({ type: 'FeatureCollection', features });
      }
      setHistoricalData(hist || []);
      setYieldMapData(yieldRows || []);
      setSobData(sobRows || []);
      setNassData(nassRows || []);
      if (m13Rows && m13Rows.length > 0) setLastM13Import(m13Rows[0]);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // D3 projection
  const { pathGen, centroids } = useMemo(() => {
    if (!geoData || !d3Lib) return { pathGen: null, centroids: {} };
    const proj = d3Lib.geoTransverseMercator().rotate([76, -40]).fitSize([800, 520], geoData);
    const pg = d3Lib.geoPath().projection(proj);
    const cents = {};
    geoData.features.forEach(f => { const c = pg.centroid(f); if (c && !isNaN(c[0])) cents[f.properties.name] = c; });
    return { pathGen: pg, centroids: cents };
  }, [geoData, d3Lib]);

  // Compute YOUR BOOK from historical_summaries + live DRP/LRP
  const yourBook = useMemo(() => {
    const book = {};
    const cropMap = { 'CORN':'CORN','SOYBEANS':'SOYBEANS','WHEAT':'WHEAT','WINTER WHEAT':'WHEAT','BARLEY':'OTHER_CROPS','DRY BEANS':'OTHER_CROPS','PROCESSING BEANS':'OTHER_CROPS','OATS':'OTHER_CROPS','TRITICALE':'OTHER_CROPS','RYE':'OTHER_CROPS','POTATOES':'OTHER_CROPS' };
    // Historical summaries (2025 crop data)
    // CRITICAL: SCO-RP and ECO-RP rows have duplicate acres — only count acres from base RP rows
    // But premium should include all plans (RP + SCO + ECO) for total premium comparison
    // Use per-layer crop year: WHEAT=2026, CORN/SOYBEANS=2025, etc.
    const cyByLayer = { CORN:'2025', SOYBEANS:'2025', WHEAT:'2026', OTHER_CROPS:'2025' };
    (historicalData || []).forEach(row => {
      if (!row || !row.county || !row.crop) return;
      const layerKey = cropMap[(row.crop||'').toUpperCase()] || null;
      if (!layerKey) return; // skip unmapped crops
      const expectedCY = cyByLayer[layerKey] || '2025';
      if (String(row.crop_year) !== expectedCY) return;
      const county = (row.county||'').toUpperCase().replace('ST LAWRENCE','ST. LAWRENCE');
      const plan = (row.type || 'RP').toUpperCase();
      const isScoEco = plan.includes('SCO') || plan.includes('ECO');
      if (!book[layerKey]) book[layerKey] = {};
      if (!book[layerKey][county]) book[layerKey][county] = { acres: 0, premium: 0, farmers: new Set() };
      // Only count acres from base RP plan — SCO/ECO rows duplicate the same acres
      if (!isScoEco) {
        book[layerKey][county].acres += (row.acres || 0);
      }
      book[layerKey][county].premium += (row.gross_premium || 0);
      if (row.farmer_name) book[layerKey][county].farmers.add(row.farmer_name);
    });
    // PRF from historical_summaries (type will be 'RI' or crop will be 'PASTURE,RANGELAND,FORAGE')
    book['PRF'] = {};
    (historicalData || []).forEach(row => {
      if (!row || !row.county || String(row.crop_year) !== '2026') return;
      const crop = (row.crop || '').toUpperCase();
      const plan = (row.type || '').toUpperCase();
      // PRF rows are either crop='PASTURE,RANGELAND,FORAGE' or type='RI'
      if (crop.includes('PASTURE') || crop.includes('FORAGE') || crop === 'PRF' || plan === 'RI') {
        const county = (row.county||'').toUpperCase().replace('ST LAWRENCE','ST. LAWRENCE');
        if (!book['PRF'][county]) book['PRF'][county] = { acres: 0, premium: 0, farmers: new Set() };
        book['PRF'][county].acres += (row.acres || 0);
        book['PRF'][county].premium += (row.gross_premium || 0);
        if (row.farmer_name) book['PRF'][county].farmers.add(row.farmer_name);
      }
    });
    // PRF fallback: also pull from live farmer prfPolicies (2026 crop year)
    (farmers || []).forEach(farmer => {
      if (!farmer.prfPolicies || farmer.prfPolicies.length === 0) return;
      farmer.prfPolicies.forEach(pol => {
        if (String(pol.cropYear || pol.crop_year || farmer.cropYear) !== '2026') return;
        const county = (pol.county || farmer.county || '').toUpperCase().replace('ST LAWRENCE','ST. LAWRENCE').trim();
        if (!county) return;
        if (!book['PRF'][county]) book['PRF'][county] = { acres: 0, premium: 0, farmers: new Set() };
        // Only add if not already counted from historical_summaries
        if (!book['PRF'][county].farmers.has(farmer.name)) {
          book['PRF'][county].acres += (Number(pol.total_acres || pol.totalAcres) || 0);
          book['PRF'][county].premium += (Number(pol.total_premium || pol.totalPremium || pol.net_premium || pol.netPremium) || 0);
          book['PRF'][county].farmers.add(farmer.name);
        }
      });
    });
    // 2026 WHEAT from live farmer data (approvedYields on farmer objects)
    // historical_summaries won't have 2026 spring/wheat data yet — pull from live crop_coverage
    // Each approvedYield row now carries its own cropYear from the policy it came from
    book['WHEAT'] = book['WHEAT'] || {};
    (farmers || []).forEach(farmer => {
      if (!farmer.approvedYields || farmer.approvedYields.length === 0) return;
      // Base plan acres + premium from approvedYields
      farmer.approvedYields.forEach(ay => {
        // Filter by the approvedYield's own crop year, not farmer.cropYear
        if (String(ay.cropYear) !== '2026') return;
        const crop = (ay.crop || '').toUpperCase();
        if (crop !== 'WHEAT' && crop !== 'WINTER WHEAT') return;
        const plan = (ay.plan || '').toUpperCase();
        const isScoEco = plan.includes('SCO') || plan.includes('ECO') || plan === 'ARPI' || plan === 'STAX' || plan === 'MP';
        const county = (ay.county || farmer.county || '').toUpperCase().replace('ST LAWRENCE','ST. LAWRENCE').trim();
        if (!county) return;
        if (!book['WHEAT'][county]) book['WHEAT'][county] = { acres: 0, premium: 0, farmers: new Set() };
        // Only count acres from base plans (RP/YP/RP-HPE) — SCO/ECO/ARPI duplicate the same acres
        if (!isScoEco) {
          book['WHEAT'][county].acres += (Number(ay.acres) || 0);
        }
        book['WHEAT'][county].premium += (Number(ay.totalPremium) || 0);
        book['WHEAT'][county].farmers.add(farmer.name);
      });
      // SCO/ECO premium from scoEcoEndorsements (acres NOT counted — they duplicate base plan acres)
      (farmer.scoEcoEndorsements || []).forEach(se => {
        const crop = (se.crop || '').toUpperCase();
        if (crop !== 'WHEAT' && crop !== 'WINTER WHEAT') return;
        const county = (se.county || farmer.county || '').toUpperCase().replace('ST LAWRENCE','ST. LAWRENCE').trim();
        if (!county) return;
        if (!book['WHEAT'][county]) book['WHEAT'][county] = { acres: 0, premium: 0, farmers: new Set() };
        book['WHEAT'][county].premium += (Number(se.totalPremium) || 0);
        book['WHEAT'][county].farmers.add(farmer.name);
      });
    });
    // DRP from live farmer data — filter by REINSURANCE YEAR 2025
    // RMA reports DRP by reinsurance year = endorsements with END DATE in calendar year 2025
    // This is different from crop year (effective date based). Must match RMA's methodology.
    book['MILK'] = {};
    (farmers || []).forEach(farmer => {
      if (!farmer.drpPolicies || farmer.drpPolicies.length === 0) return;
      const endMap = {};
      let farmerDrpCounty = '';
      farmer.drpPolicies.forEach(pol => {
        const polCounty = (pol.county || farmer.county || '').toUpperCase().replace('ST LAWRENCE','ST. LAWRENCE').trim();
        if (polCounty) farmerDrpCounty = polCounty;
        (pol.endorsements || []).forEach(end => {
          // Extract year from endDate (could be "MM/DD/YYYY" or "YYYY-MM-DD")
          const ed = end.endDate || end.end_date || '';
          let endYear = null;
          const isoMatch = String(ed).match(/^(\d{4})-/);
          const mdyMatch = String(ed).match(/(\d{4})$/);
          if (isoMatch) endYear = parseInt(isoMatch[1]);
          else if (mdyMatch) endYear = parseInt(mdyMatch[1]);
          
          if (endYear === 2025) {
            const dk = `${ed}|${end.endNum||end.end_num||''}`;
            const existing = endMap[dk];
            if (!existing) {
              endMap[dk] = { ...end, _county: polCounty || farmerDrpCounty };
            }
          }
        });
      });
      Object.values(endMap).forEach(end => {
        const county = end._county || farmerDrpCounty;
        if (!county) return;
        if (!book['MILK'][county]) book['MILK'][county] = { lbs: 0, premium: 0, farmers: new Set() };
        book['MILK'][county].lbs += (Number(end.declaredMilkProd || end.declared_milk_prod) || 0);
        book['MILK'][county].premium += (Number(end.totalPremium || end.total_premium) || 0);
        book['MILK'][county].farmers.add(farmer.name);
      });
    });
    // LRP from live farmer data — filter by REINSURANCE YEAR 2025 (endDate in 2025)
    // County comes from the ENDORSEMENT level, not the policy level
    // LRP premium field is 'premium' (gross), not 'totalPremium'
    book['LRP'] = {};
    (farmers || []).forEach(farmer => {
      if (!farmer.lrpPolicies) return;
      farmer.lrpPolicies.forEach(pol => {
        (pol.endorsements || []).forEach(end => {
          // Filter by reinsurance year = endDate in 2025
          const ed = end.endDate || end.end_date || '';
          let endYear = null;
          const isoMatch = String(ed).match(/^(\d{4})-/);
          const mdyMatch = String(ed).match(/(\d{4})$/);
          if (isoMatch) endYear = parseInt(isoMatch[1]);
          else if (mdyMatch) endYear = parseInt(mdyMatch[1]);
          if (endYear !== 2025) return;

          // County: prefer endorsement-level county, then policy, then farmer
          const county = (end.county || pol.county || farmer.county || '').toUpperCase().replace('ST LAWRENCE','ST. LAWRENCE').trim();
          if (!county) return;
          if (!book['LRP'][county]) book['LRP'][county] = { head: 0, premium: 0, farmers: new Set() };
          book['LRP'][county].head += (Number(end.head) || 0);
          book['LRP'][county].premium += (Number(end.premium) || 0);
          book['LRP'][county].farmers.add(farmer.name);
        });
      });
    });
    // Convert Sets to counts
    Object.values(book).forEach(layer => {
      Object.values(layer).forEach(d => { if (d.farmers instanceof Set) d.farmerCount = d.farmers.size; });
    });
    return book;
  }, [historicalData, farmers]);

  const LAYERS = [
    { key:'CORN', label:'Corn', icon:'🌽', unit:'acres', qf:'acres', cy:2025 },
    { key:'SOYBEANS', label:'Soybeans', icon:'🫘', unit:'acres', qf:'acres', cy:2025 },
    { key:'WHEAT', label:'Wheat', icon:'🌾', unit:'acres', qf:'acres', cy:2026 },
    { key:'PRF', label:'PRF', icon:'🌧️', unit:'acres', qf:'acres', cy:2026 },
    { key:'MILK', label:'DRP (Milk)', icon:'🥛', unit:'lbs', qf:'lbs', cy:2025 },
    { key:'LRP', label:'LRP', icon:'🐄', unit:'head', qf:'head', cy:2025 },
    { key:'YIELD_CORN', label:'Corn Yield', icon:'📊', unit:'bu/ac', qf:'yield', isYield:true, crop:'CORN' },
    { key:'YIELD_SOYBEANS', label:'Soy Yield', icon:'📊', unit:'bu/ac', qf:'yield', isYield:true, crop:'SOYBEANS' },
    { key:'YIELD_WHEAT', label:'Wheat Yield', icon:'📊', unit:'bu/ac', qf:'yield', isYield:true, crop:'WHEAT' },
  ];
  const ali = LAYERS.find(l => l.key === activeLayer) || LAYERS[0];
  const isYieldLayer = !!ali.isYield;

  // Compute yield-by-county for the active yield layer
  // Filter identically to the yield dashboard: excluded_from_avg=false AND valid yield
  const yieldCountyData = useMemo(() => {
    if (!isYieldLayer) return {};
    const crop = ali.crop;
    // RMA Final county yields — official released figures (NY only). Keyed by
    // county name; we surface finalCountyYield (skip counties RMA hasn't
    // published a final for — Suffolk/Sullivan/Warren etc.). No acres basis.
    if (yieldSource === 'rma') {
      const rmaNY = RMA_COUNTY_EXPECTED_REVENUE[2025]?.[crop]?.NY || {};
      const result = {};
      Object.entries(rmaNY).forEach(([c, d]) => {
        if (d && d.finalCountyYield != null) result[c] = { yield: d.finalCountyYield, acres: null, source: 'rma' };
      });
      return result;
    }
    const byCounty = {};
    yieldMapData
      .filter(r => r.crop === crop && !r.excluded_from_avg && isValidProdRow(r))
      .forEach(r => {
        const c = (r.county||'').toUpperCase();
        if (!byCounty[c]) byCounty[c] = { prod:0, acres:0 };
        byCounty[c].prod  += Number(r.production||0);
        byCounty[c].acres += Number(r.acres||0);
      });
    const result = {};
    Object.entries(byCounty).forEach(([c, d]) => {
      result[c] = { yield: d.acres>0 ? d.prod/d.acres : null, acres: d.acres, source: 'book' };
    });
    return result;
  }, [isYieldLayer, ali, yieldMapData, yieldSource]);

  // Yield color scale — different ranges per crop
  const YIELD_SCALES = {
    CORN:     [{min:0,max:80,c:'#eef6e8',l:'<80'},{min:80,max:110,c:'#b8dfa0',l:'80–110'},{min:110,max:140,c:'#6dba52',l:'110–140'},{min:140,max:170,c:'#2e8a1e',l:'140–170'},{min:170,max:999,c:'#0f4f08',l:'170+'}],
    SOYBEANS: [{min:0,max:25,c:'#eef6e8',l:'<25'},{min:25,max:35,c:'#b8dfa0',l:'25–35'},{min:35,max:45,c:'#6dba52',l:'35–45'},{min:45,max:55,c:'#2e8a1e',l:'45–55'},{min:55,max:999,c:'#0f4f08',l:'55+'}],
    WHEAT:    [{min:0,max:50,c:'#eef6e8',l:'<50'},{min:50,max:70,c:'#b8dfa0',l:'50–70'},{min:70,max:85,c:'#6dba52',l:'70–85'},{min:85,max:100,c:'#2e8a1e',l:'85–100'},{min:100,max:999,c:'#0f4f08',l:'100+'}],
  };
  const yieldScale = YIELD_SCALES[ali.crop] || YIELD_SCALES.CORN;
  const getYieldFill = (yld) => {
    if (yld == null || yld === 0) return '#f0f0ee';
    for (const s of yieldScale) if (yld >= s.min && yld < s.max) return s.c;
    return '#f0f0ee';
  };

  // Color scales
  const SC = [{min:0,max:.001,c:'#e9eceb',l:'0%'},{min:.001,max:.05,c:'#d2e6ce',l:'<5%'},{min:.05,max:.15,c:'#a1d498',l:'5–15%'},{min:.15,max:.3,c:'#68bb5c',l:'15–30%'},{min:.3,max:.5,c:'#389230',l:'30–50%'},{min:.5,max:1.01,c:'#1b5e16',l:'50%+'}];
  const OC = [{min:0,max:1,c:'#e9eceb',l:'No data'},{min:1,max:20,c:'#fde8d0',l:'Low'},{min:20,max:40,c:'#f9c97a',l:'Moderate'},{min:40,max:65,c:'#e8882e',l:'High'},{min:65,max:101,c:'#b83d0a',l:'Very High'}];
  const gc = (v,arr) => { for (const s of arr) if (v >= s.min && v < s.max) return s.c; return '#e9eceb'; };

  // County data for active layer
  // Helper — normalize county names so "ST. LAWRENCE", "St. Lawrence", "St Lawrence" all match
  const normCty = (s) => String(s||'').toUpperCase().trim().replace(/\s+COUNTY$/,'').replace(/\./g,'').replace(/\s+/g,' ');

  // SOB data for the active layer, keyed by normalized county name
  // Uses ali.cy (CY2025 for row crops / dairy / livestock, CY2026 for wheat / PRF)
  const sobByCounty = useMemo(() => {
    const out = {};
    sobData.forEach(r => {
      if (r.crop_year !== ali.cy) return;
      if (r.crop !== activeLayer) return;
      out[normCty(r.county)] = r;
    });
    return out;
  }, [sobData, ali.cy, activeLayer]);

  // NASS planted acres (or harvested for HAY), latest year per county.
  // Only populate for row crops and wheat — livestock / dairy / PRF have no NASS acreage basis.
  const nassByCounty = useMemo(() => {
    const nassCrop = (activeLayer === 'CORN' || activeLayer === 'SOYBEANS' || activeLayer === 'WHEAT') ? activeLayer : null;
    if (!nassCrop) return {};
    const statType = 'ACRES_PLANTED';  // HAY handled separately if ever added as a market-map layer
    const out = {};
    nassData.forEach(r => {
      if (r.crop !== nassCrop) return;
      if (r.stat_type !== statType) return;
      const k = normCty(r.county);
      const existing = out[k];
      // Keep latest year; break ties on fetched_at
      if (!existing || r.year > existing.year || (r.year === existing.year && r.fetched_at > existing.fetched_at)) {
        out[k] = r;
      }
    });
    return out;
  }, [nassData, activeLayer]);

  // County data for active layer — fused from sob_data (mkt) + yourBook (ours) + nass_data (planted)
  const countyData = useMemo(() => {
    const bk = yourBook[activeLayer] || {};
    const map = {};
    // Total state premium across the layer's SOB rows — drives opportunity-score sizing
    let stPrem = 0;
    Object.values(sobByCounty).forEach(r => { stPrem += Number(r.premium||0); });
    // Union of counties with SOB data OR our book data
    const allCounties = new Set([...Object.keys(sobByCounty), ...Object.keys(bk).map(normCty)]);
    allCounties.forEach(nkey => {
      const s = sobByCounty[nkey];
      // yourBook keys are raw-cased (e.g. "ST. LAWRENCE") — find matching key by normalizing
      let bKey = null;
      for (const k of Object.keys(bk)) { if (normCty(k) === nkey) { bKey = k; break; } }
      const b = (bKey && bk[bKey]) || {};
      // Display key for the map: prefer original SOB casing, else yourBook casing, else the normalized key
      const displayKey = s?.county || bKey || nkey;
      const mP = Number(s?.premium||0);
      const oP = Number(b.premium||0);
      const share = mP > 0 ? oP / mP : 0;
      const sizePct = stPrem > 0 ? mP / stPrem : 0;
      const gap = 1 - Math.min(share, 1);
      let opp = mP < 25000 ? 0 : Math.round(sizePct * gap * 500);
      opp = Math.min(opp, 100);
      // Market quantity — acres for row crops / wheat / PRF, units_value for MILK (lbs) / LRP (head)
      const mktQty = s?.net_reported_acres != null ? Number(s.net_reported_acres) : Number(s?.units_value || 0);
      // Our quantity in the same unit
      const ourQty = b[ali.qf] || b.acres || b.lbs || b.head || 0;
      // NASS planted acres (row crops / wheat only)
      const n = nassByCounty[nkey];
      const plantedAcres = n ? Number(n.value||0) : null;
      const nassYear = n ? n.year : null;
      const countyPenetration = (plantedAcres && mktQty && ali.unit === 'acres') ? (Number(mktQty) / plantedAcres) : null;
      map[displayKey.toUpperCase()] = {
        mktPolicies: Number(s?.policies_count||0),
        mktQty,
        mktPremium: mP,
        mktLiability: Number(s?.liability||0),
        mktIndemnity: Number(s?.indemnity||0),
        ourQty,
        ourPremium: oP,
        ourFarmers: b.farmerCount||0,
        share,
        oppScore: opp,
        plantedAcres,
        nassYear,
        nassStatType: n ? n.stat_type : null,
        countyPenetration,
        fill: viewMode === 'opportunity' ? gc(opp, OC) : gc(share, SC),
      };
    });
    return map;
  }, [activeLayer, yourBook, viewMode, ali, sobByCounty, nassByCounty]);

  // State rollup
  const stateRollup = useMemo(() => {
    const t = { mktPolicies:0, mktQty:0, mktPremium:0, ourQty:0, ourPremium:0, mktIndemnity:0, counties:0 };
    Object.values(countyData).forEach(d => {
      t.mktPolicies += d.mktPolicies; t.mktQty += d.mktQty; t.mktPremium += d.mktPremium;
      t.ourQty += d.ourQty; t.ourPremium += d.ourPremium; t.mktIndemnity += d.mktIndemnity; t.counties++;
    });
    t.stateShare = t.mktPremium > 0 ? t.ourPremium / t.mktPremium : 0;
    t.lossRatio = t.mktPremium > 0 ? t.mktIndemnity / t.mktPremium : 0;
    return t;
  }, [countyData]);

  const getKey = (name) => { if (!name) return ''; const u = name.toUpperCase(); if (isYieldLayer) { if (yieldCountyData[u]) return u; const cl=u.replace(/[\s.]/g,''); for(const k of Object.keys(yieldCountyData)) if(k.replace(/[\s.]/g,'')===cl) return k; return u; } if (countyData[u]) return u; const cl = u.replace(/[\s.]/g,''); for (const k of Object.keys(countyData)) if (k.replace(/[\s.]/g,'')===cl) return k; return u; };
  const getFill = (n) => { if (isYieldLayer) { const k=getKey(n); const e=yieldCountyData[k]; return e ? getYieldFill(e.yield) : '#f0f0ee'; } const e = countyData[getKey(n)]; return e ? e.fill : '#f2f2f0'; };
  const getData = (n) => { if (isYieldLayer) { const k=getKey(n); return yieldCountyData[k]||null; } return countyData[getKey(n)] || null; };
  const fmt$m = (v) => { if (!v) return '$0'; return v >= 1e6 ? '$'+(v/1e6).toFixed(1)+'M' : v >= 1000 ? '$'+Math.round(v/1000)+'K' : '$'+Math.round(v); };
  const fmtPctM = (v) => v ? (v*100).toFixed(1)+'%' : '0%';
  const fmtQtyM = (v,u) => { if (!v) return '0'; if (u==='lbs') return v>=1e6?(v/1e6).toFixed(1)+'M lbs':Math.round(v/1000)+'K lbs'; if (u==='head') return v.toLocaleString()+' hd'; return v.toLocaleString()+' ac'; };
  // Compact date formatter for freshness indicators ("Apr 24" / "—")
  const fmtDate = (iso) => { if (!iso) return '—'; const d = new Date(iso); if (isNaN(d.getTime())) return '—'; return d.toLocaleDateString('en-US', { month:'short', day:'numeric' }); };
  // Latest sync timestamps across each source — computed once per data change
  const dataFreshness = useMemo(() => {
    const maxTs = (rows, field) => rows.reduce((m, r) => (r[field] && (!m || r[field] > m)) ? r[field] : m, null);
    return {
      sob:  maxTs(sobData,  'fetched_at'),
      nass: maxTs(nassData, 'fetched_at'),
      m13:  lastM13Import?.imported_at || null,
    };
  }, [sobData, nassData, lastM13Import]);

  const ranked = useMemo(() => Object.entries(countyData).sort((a,b) => viewMode==='opportunity'?(b[1].oppScore||0)-(a[1].oppScore||0):(b[1].share||0)-(a[1].share||0)), [countyData, viewMode]);
  // Latest NASS year for the active crop — informs the "NASS latest: YYYY" hint in the header
  const latestNassYear = useMemo(() => {
    const vals = Object.values(nassByCounty).map(r => r.year).filter(Boolean);
    return vals.length > 0 ? Math.max(...vals) : null;
  }, [nassByCounty]);
  const nassLagsSob = latestNassYear && ali.cy && latestNassYear < ali.cy;
  const legend = viewMode === 'opportunity' ? OC : SC;
  const selData = selected ? getData(selected) : null;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:isMobile?'flex-start':'center', justifyContent:'space-between', marginBottom:8, flexWrap:'wrap', gap:8, flexDirection:isMobile?'column':'row' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:isMobile?18:22 }}>🗺️</span>
          <div>
            <h2 style={{ margin:0, fontSize:isMobile?15:18, fontWeight:700, color:'#1a2e1a', fontFamily:"'Lora',serif" }}>{isYieldLayer ? `${ali.label} — CY ${ali.cy || 2025}` : `NY Market Map — CY ${ali.cy || 2025}`}</h2>
            <span style={{ fontSize:isMobile?10:11, color:'#6b7c6b' }}>{ali.label} · {isYieldLayer ? (yieldSource === 'rma' ? 'RMA Final county yield · CY2025 (PM-26-029)' : 'From your book · M13 APH') : (viewMode === 'share' ? 'Market Share' : 'Opportunity')}{!isYieldLayer && nassLagsSob ? <span style={{ color:'#b07820', marginLeft:6 }}>· NASS CY{ali.cy} not yet released</span> : null}</span>
            <div style={{ fontSize:isMobile?9:10, color:'#96a596', marginTop:2, display:'flex', gap:8, flexWrap:'wrap' }}>
              <span title="Your book — last M13 sync">📥 M13: {fmtDate(dataFreshness.m13)}</span>
              <span title="SOB market data — last upload">📊 SOB: {fmtDate(dataFreshness.sob)}</span>
              <span title={`NASS planted acres — last sync${latestNassYear ? ' (latest data: CY'+latestNassYear+')' : ''}`}>🌾 NASS: {fmtDate(dataFreshness.nass)}{latestNassYear ? ` (CY${latestNassYear})` : ''}</span>
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:isMobile?4:6, flexWrap:'wrap' }}>
          {(isYieldLayer ? (() => {
            if (yieldSource === 'rma') {
              // RMA finals carry no acres — use a simple county average.
              const entries = Object.values(yieldCountyData).filter(d => d.yield != null && d.yield > 0);
              const avg = entries.length ? entries.reduce((s,d) => s + d.yield, 0) / entries.length : 0;
              return [
                ['Counties', entries.length+'', false],
                ['Source', 'RMA Final', false],
                ['Avg Yld', avg ? avg.toFixed(1) : '—', true],
                ['Unit', 'bu/ac', false],
              ];
            }
            // Compute weighted-average yield across all counties (weighted by acres)
            const entries = Object.values(yieldCountyData).filter(d => d.yield != null && d.yield > 0 && d.acres > 0);
            const totAc = entries.reduce((s,d) => s + d.acres, 0);
            const wtdYld = totAc > 0 ? entries.reduce((s,d) => s + d.yield * d.acres, 0) / totAc : 0;
            const counties = entries.length;
            return [
              ['Counties', counties+'', false],
              ['Acres', totAc > 1000 ? Math.round(totAc/1000)+'K' : Math.round(totAc)+'', false],
              ['Avg Yld', wtdYld ? wtdYld.toFixed(1) : '—', true],
              ['Unit', 'bu/ac', false],
            ];
          })() : [
            ['Mkt', fmt$m(stateRollup.mktPremium), false],
            ['Ours', fmt$m(stateRollup.ourPremium), false],
            ['Share', fmtPctM(stateRollup.stateShare), true],
            ['L/R', fmtPctM(stateRollup.lossRatio), false],
          ]).map(([lbl,val,acc],i) => (
            <div key={i} style={{ background:acc?'#2d4a2d':'#fff', border:acc?'none':'1px solid #d4ddd4', borderRadius:6, padding:isMobile?'2px 7px':'3px 10px', textAlign:'center' }}>
              <div style={{ fontSize:isMobile?7:8, fontWeight:600, color:acc?'#8ebb8e':'#6b7c6b', textTransform:'uppercase' }}>{lbl}</div>
              <div style={{ fontSize:isMobile?11:13, fontWeight:700, color:acc?'#fff':'#1a2e1a', marginTop:1 }}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Layer + view toggle */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:6 }}>
        <div style={{ display:'flex', gap:isMobile?3:4, flexWrap:'wrap', overflowX:isMobile?'auto':'visible', WebkitOverflowScrolling:'touch', maxWidth:'100%' }}>
          {LAYERS.map(lyr => (
            <button key={lyr.key} onClick={() => { setActiveLayer(lyr.key); setSelected(null); }}
              style={{ padding:isMobile?'4px 8px':'4px 10px', fontSize:isMobile?11:12, fontWeight:activeLayer===lyr.key?700:500,
                border:activeLayer===lyr.key?'2px solid #2d4a2d':'1px solid #c5d4c5', borderRadius:16, cursor:'pointer',
                background:activeLayer===lyr.key?'#2d4a2d':'#fff', color:activeLayer===lyr.key?'#fff':'#3a5a3a', whiteSpace:'nowrap', flexShrink:0 }}>
              {lyr.icon} {isMobile ? lyr.label.replace('DRP (Milk)','DRP').replace('Soybeans','Soy') : lyr.label}
            </button>
          ))}
        </div>
        {isYieldLayer ? (
          <div style={{ display:'flex', gap:2, background:'#eee', borderRadius:8, padding:2, flexShrink:0 }}>
            <button onClick={() => setYieldSource('book')} title="Your agency book (M13 APH)" style={{ padding:isMobile?'3px 8px':'4px 12px', fontSize:isMobile?10:11, fontWeight:600, border:'none', borderRadius:6, cursor:'pointer', background:yieldSource==='book'?'#2d4a2d':'transparent', color:yieldSource==='book'?'#fff':'#555' }}>📥 Book</button>
            <button onClick={() => setYieldSource('rma')} title="Official RMA Final county yields (NY only)" style={{ padding:isMobile?'3px 8px':'4px 12px', fontSize:isMobile?10:11, fontWeight:600, border:'none', borderRadius:6, cursor:'pointer', background:yieldSource==='rma'?'#1a5e8a':'transparent', color:yieldSource==='rma'?'#fff':'#555' }}>🏛️ RMA Final</button>
          </div>
        ) : (
          <div style={{ display:'flex', gap:2, background:'#eee', borderRadius:8, padding:2, flexShrink:0 }}>
            <button onClick={() => setViewMode('share')} style={{ padding:isMobile?'3px 8px':'4px 12px', fontSize:isMobile?10:11, fontWeight:600, border:'none', borderRadius:6, cursor:'pointer', background:viewMode==='share'?'#2d4a2d':'transparent', color:viewMode==='share'?'#fff':'#555' }}>Share</button>
            <button onClick={() => setViewMode('opportunity')} style={{ padding:isMobile?'3px 8px':'4px 12px', fontSize:isMobile?10:11, fontWeight:600, border:'none', borderRadius:6, cursor:'pointer', background:viewMode==='opportunity'?'#b83d0a':'transparent', color:viewMode==='opportunity'?'#fff':'#555' }}>🎯 Opp</button>
          </div>
        )}
      </div>

      {/* Map + sidebar */}
      <div style={{ display:'flex', gap:12, alignItems:'flex-start', flexDirection:isMobile?'column':'row' }}>
        {/* MAP */}
        <div style={{ flex:1, minWidth:0, width:isMobile?'100%':undefined, background:'#fff', borderRadius:10, border:'1px solid #d4ddd4', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,.05)' }}>
          {loading ? (
            <div style={{ padding:isMobile?40:80, textAlign:'center', color:'#6b7c6b' }}><div style={{ fontSize:28, marginBottom:8 }}>🗺️</div>Loading map...</div>
          ) : geoData && pathGen ? (<>
            <svg viewBox="0 0 800 520" style={{ width:'100%', height:'auto', display:'block' }}>
              <rect width="800" height="520" fill="#dae5ee" />
              {geoData.features.map(f => {
                const nm=f.properties.name, d=pathGen(f); if(!d) return null;
                const isH=hovered===nm, isS=selected===nm, fill=getFill(nm), has=!!getData(nm);
                return <path key={f.id} d={d} fill={isS?'#fff3b0':isH?(has?'#ffe082':'#e4e4e4'):fill} stroke={isS?'#a67c00':isH?'#555':'#8fa88f'} strokeWidth={isS?2.2:isH?1.2:0.5} strokeLinejoin="round" style={{ cursor:'pointer', transition:'fill .15s' }} onMouseEnter={()=>!isMobile&&setHovered(nm)} onMouseLeave={()=>!isMobile&&setHovered(null)} onClick={()=>{setSelected(isS?null:nm); if(isMobile) setShowSidebar(true);}} />;
              })}
              {geoData.features.map(f => {
                const nm=f.properties.name; if(NYC_SET.has(nm)) return null;
                const c=centroids[nm]; if(!c) return null;
                const area=pathGen.area(f); if(area<80) return null;
                const fs=area>3000?8.5:area>1200?7:area>400?6:5.5;
                if (isYieldLayer) {
                  const k=getKey(nm); const yd=yieldCountyData[k];
                  const yld=yd?.yield;
                  const hasData=yld!=null && yld>0;
                  const fillLum = hasData && yd.yield>140 ? '#fff' : '#1a2e1a';
                  return (
                    <g key={'lbl'+f.id} style={{ pointerEvents:'none', userSelect:'none' }}>
                      <text x={c[0]} y={hasData?c[1]-3:c[1]} textAnchor="middle" dominantBaseline="central"
                        fontSize={fs} fontWeight={selected===nm?700:400}
                        fill={selected===nm?'#5a3e00':hasData?fillLum:'#aaa'}>
                        {nm.length>9?nm.slice(0,8)+'…':nm}
                      </text>
                      {hasData && area>300 && (
                        <text x={c[0]} y={c[1]+fs+1} textAnchor="middle" dominantBaseline="central"
                          fontSize={Math.max(fs-0.5,4)} fontWeight={700}
                          fill={selected===nm?'#5a3e00':fillLum}>
                          {yld.toFixed(1)}
                        </text>
                      )}
                    </g>
                  );
                }
                return <text key={'l'+f.id} x={c[0]} y={c[1]} textAnchor="middle" dominantBaseline="central" fontSize={fs} fontWeight={selected===nm?700:500} fill={selected===nm?'#5a3e00':getData(nm)?'#1a3a1a':'#6b6b6b'} stroke='rgba(255,255,255,0.85)' strokeWidth={fs*0.3} paintOrder='stroke' strokeLinejoin='round' style={{ pointerEvents:'none', userSelect:'none' }}>{nm}</text>;
              })}
              {!isMobile && hovered && !selected && (() => {
                const c=centroids[hovered], d=getData(hovered); if(!c) return null;
                // Only show planted/penetration if NASS year matches the SOB year we're viewing.
                // Showing CY2024 NASS acres alongside CY2025 SOB acres is an apples-to-oranges comparison.
                const yearsMatch = d && d.nassYear != null && d.nassYear === ali.cy;
                const hasPlanted = d && !isYieldLayer && yearsMatch && d.plantedAcres != null && d.plantedAcres > 0;
                const bw=185, bh=d?(hasPlanted?70:56):28, tx=Math.min(Math.max(c[0],bw/2+5),795-bw/2), ty=Math.max(c[1]-(bh+15),8);
                return <g style={{ pointerEvents:'none' }}>
                  <rect x={tx-bw/2} y={ty} width={bw} height={bh} rx={6} fill="rgba(26,46,26,.94)" />
                  <polygon points={`${tx-5},${ty+bh} ${tx},${ty+bh+7} ${tx+5},${ty+bh}`} fill="rgba(26,46,26,.94)" />
                  <text x={tx} y={ty+15} textAnchor="middle" fontSize="10.5" fill="#fff" fontWeight="600">{hovered} County</text>
                  {d && !isYieldLayer && <text x={tx} y={ty+31} textAnchor="middle" fontSize="8.5" fill="#a8d4a8">Mkt: {fmt$m(d.mktPremium)} · Share: {fmtPctM(d.share)}</text>}
                  {d && !isYieldLayer && viewMode==='opportunity' && !hasPlanted && <text x={tx} y={ty+44} textAnchor="middle" fontSize="8.5" fill="#f9c97a">Opportunity: {d.oppScore}/100</text>}
                  {hasPlanted && <text x={tx} y={ty+44} textAnchor="middle" fontSize="8" fill="#c8dfc8">Planted: {Math.round(d.plantedAcres).toLocaleString()} ac ({d.nassYear}) · {d.countyPenetration!=null?(d.countyPenetration*100).toFixed(0)+'%':'—'} insured</text>}
                  {hasPlanted && viewMode==='opportunity' && <text x={tx} y={ty+58} textAnchor="middle" fontSize="8.5" fill="#f9c97a">Opportunity: {d.oppScore}/100</text>}
                  {d && isYieldLayer && <text x={tx} y={ty+31} textAnchor="middle" fontSize="9" fill="#ffe082" fontWeight="700">{d.yield!=null?d.yield.toFixed(1)+' bu/ac':'No yield data'}</text>}
                  {d && isYieldLayer && <text x={tx} y={ty+44} textAnchor="middle" fontSize="8" fill="#a8d4a8">{d.acres!=null?Math.round(d.acres).toLocaleString()+' ac reported':''}</text>}
                  {!d && <text x={tx} y={ty+15} textAnchor="middle" fontSize="8.5" fill="#bbb">{hovered} — No data</text>}
                </g>;
              })()}
            </svg>
            <div style={{ display:'flex', gap:isMobile?4:7, flexWrap:'wrap', padding:isMobile?'4px 8px':'6px 12px', borderTop:'1px solid #eee' }}>
              {(isYieldLayer ? yieldScale : legend).map((it,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:2, fontSize:isMobile?9:10.5, color:'#5a6b5a' }}>
                  <div style={{ width:isMobile?9:11, height:isMobile?9:11, borderRadius:2, background:it.c, border:'1px solid rgba(0,0,0,.1)' }}/>
                  {it.l}{isYieldLayer?' bu/ac':''}
                </div>
              ))}
              <div style={{ display:'flex', alignItems:'center', gap:2, fontSize:isMobile?9:10.5, color:'#aaa' }}><div style={{ width:isMobile?9:11, height:isMobile?9:11, borderRadius:2, background:'#f2f2f0', border:'1px solid rgba(0,0,0,.1)' }}/>No data</div>
            </div>
          </>) : null}
        </div>

        {/* SIDEBAR — on mobile, show as expandable panel below map */}
        {(showSidebar || !isMobile) && (
          <div style={{ width:isMobile?'100%':280, flexShrink:0, display:'flex', flexDirection:'column', gap:10 }}>
            {/* Mobile toggle */}
            {isMobile && (
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:13, fontWeight:700, color:'#1a2e1a' }}>
                  {selected ? selected + ' County' : (viewMode==='opportunity'?'🎯 Opportunity':'📊 Rankings')}
                </span>
                <button onClick={() => { setShowSidebar(false); if(selected) setSelected(null); }} style={{ background:'#eee', border:'none', borderRadius:6, padding:'4px 10px', fontSize:11, fontWeight:600, cursor:'pointer', color:'#555' }}>✕ Close</button>
              </div>
            )}

            {selected && (
              <div style={{ background:'#fff', borderRadius:10, border:'2px solid #c8a630', padding:12, boxShadow:'0 2px 8px rgba(0,0,0,.07)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                  <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:'#1a2e1a' }}>{selected} County</h3>
                  <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', fontSize:14, cursor:'pointer', color:'#aaa', padding:0 }}>✕</button>
                </div>
                {selData && isYieldLayer ? (<>
                  <div style={{ textAlign:'center', padding:'8px 0 6px' }}>
                    <div style={{ fontSize:28, fontWeight:800, color:'#e8a820', fontVariantNumeric: "tabular-nums" }}>
                      {selData.yield!=null ? selData.yield.toFixed(1) : '—'}
                    </div>
                    <div style={{ fontSize:11, color:'#60735f' }}>bu/ac · {yieldSource==='rma' ? '2025 RMA final' : '2025 actual'}</div>
                  </div>
                  {(yieldSource==='rma' ? [
                    ['Crop', ali.crop.charAt(0)+ali.crop.slice(1).toLowerCase()],
                    ['Source', 'RMA Final (PM-26-029)'],
                  ] : [
                    ['Reported Acres', selData.acres!=null?Math.round(selData.acres).toLocaleString()+' ac':'—'],
                    ['Crop', ali.crop.charAt(0)+ali.crop.slice(1).toLowerCase()],
                    ['Source', 'Your book (M13 APH)'],
                  ]).map(([l,v],i)=>(
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid #f0f0f0' }}><span style={{ fontSize:11.5, color:'#666' }}>{l}</span><span style={{ fontSize:11.5, fontWeight:600, color:'#1a2e1a' }}>{v}</span></div>
                  ))}
                </>) : selData ? (<>
                  {[['Market Premium', fmt$m(selData.mktPremium)],['Our Premium', fmt$m(selData.ourPremium)]].map(([l,v],i)=>(
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid #f0f0f0' }}><span style={{ fontSize:11.5, color:'#666' }}>{l}</span><span style={{ fontSize:11.5, fontWeight:600, color:'#1a2e1a' }}>{v}</span></div>
                  ))}
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid #f0f0f0' }}><span style={{ fontSize:11.5, color:'#666' }}>Market Share</span><span style={{ fontSize:13, fontWeight:700, color:'#2d4a2d' }}>{fmtPctM(selData.share)}</span></div>
                  <div style={{ marginTop:4, height:14, background:'#e8e8e8', borderRadius:7, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:Math.min(selData.share*100,100)+'%', background:'linear-gradient(90deg,#4aad3e,#1d5f17)', borderRadius:7, transition:'width .3s', minWidth:selData.share>0?3:0 }} />
                  </div>
                  <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #eee' }}>
                    {[
                      ['Mkt '+ali.unit, fmtQtyM(selData.mktQty, ali.unit)],
                      ['Our '+ali.unit, fmtQtyM(selData.ourQty, ali.unit)],
                      ['Our Farmers', selData.ourFarmers || 0],
                      ['Mkt Policies', selData.mktPolicies],
                      ['Mkt Loss Ratio', selData.mktPremium>0?fmtPctM(selData.mktIndemnity/selData.mktPremium):'N/A'],
                    ]
                      .concat((selData.plantedAcres && selData.nassYear === ali.cy) ? [
                        ['NASS Planted ('+selData.nassYear+')', Math.round(selData.plantedAcres).toLocaleString()+' ac'],
                        ['County Insured', selData.countyPenetration!=null ? fmtPctM(selData.countyPenetration) : '—'],
                      ] : [])
                      .concat(viewMode==='opportunity'?[['Opp Score', selData.oppScore+'/100']]:[]).map(([l,v],i)=>(
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid #f0f0f0' }}><span style={{ fontSize:11.5, color:'#666' }}>{l}</span><span style={{ fontSize:11.5, fontWeight:600, color:l.includes('Opp')?'#b83d0a':l.includes('NASS')||l.includes('County Insured')?'#1a5e8a':'#1a2e1a' }}>{v}</span></div>
                    ))}
                  </div>
                </>) : <p style={{ fontSize:12, color:'#aaa', margin:'6px 0 0' }}>No {isYieldLayer?'yield':'RMA'} data for {selected}</p>}
              </div>
            )}

            {/* Rankings */}
            <div style={{ background:'#fff', borderRadius:10, border:'1px solid #d4ddd4', boxShadow:'0 1px 3px rgba(0,0,0,.05)', overflow:'hidden' }}>
              <div style={{ padding:'8px 12px', borderBottom:'1px solid #eee', background:'#fafbf9' }}>
                <h3 style={{ margin:0, fontSize:12, fontWeight:600, color:'#1a2e1a' }}>
                  {isYieldLayer ? `📊 ${ali.label} Ranking by County` : viewMode==='opportunity'?'🎯 Opportunity Ranking':'📊 Market Share Ranking'}
                </h3>
              </div>
              <div style={{ maxHeight:isMobile?300:400, overflowY:'auto' }}>
                {isYieldLayer ? (
                  Object.entries(yieldCountyData)
                    .filter(([,d]) => d.yield != null && d.yield > 0)
                    .sort(([,a],[,b]) => (b.yield||0)-(a.yield||0))
                    .map(([county, data], i) => {
                      const nm = county.charAt(0)+county.slice(1).toLowerCase();
                      const isAct = selected && selected.toUpperCase().replace(/[\s.]/g,'')===county.replace(/[\s.]/g,'');
                      return (
                        <div key={county} onClick={()=>{ const m=Object.values(FIPS_TO_NAME_GLOBAL).find(n=>n.toUpperCase()===county); setSelected(isAct?null:m||nm); }}
                          style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 11px', cursor:'pointer', fontSize:11.5,
                            background:isAct?'#fff8e1':i%2===0?'#fff':'#fafbf9', borderLeft:isAct?'3px solid #c8a630':'3px solid transparent' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                            <span style={{ fontSize:10, color:'#999', width:14, textAlign:'right' }}>{i+1}.</span>
                            <div style={{ width:9, height:9, borderRadius:2, background:getYieldFill(data.yield), border:'1px solid rgba(0,0,0,.08)' }} />
                            <span style={{ fontWeight:500, color:'#1a2e1a' }}>{nm}</span>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <span style={{ fontWeight:700, color:'#e8a820', fontSize:12, fontVariantNumeric: "tabular-nums" }}>{data.yield.toFixed(1)}</span>
                            <span style={{ fontSize:10, color:'#999', marginLeft:3 }}>bu/ac</span>
                          </div>
                        </div>
                      );
                    })
                ) : ranked.map(([county, data], i) => {
                  const nm = county.charAt(0) + county.slice(1).toLowerCase().replace(/\. l/g, '. L');
                  const isAct = selected && selected.toUpperCase().replace(/[\s.]/g,'') === county.replace(/[\s.]/g,'');
                  return (
                    <div key={county} onClick={() => { const m = Object.values(FIPS_TO_NAME_GLOBAL).find(n => n.toUpperCase()===county); setSelected(isAct?null:m||nm); }}
                      style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 11px', cursor:'pointer', fontSize:11.5,
                        background:isAct?'#fff8e1':i%2===0?'#fff':'#fafbf9', borderLeft:isAct?'3px solid #c8a630':'3px solid transparent' }}
                      onMouseEnter={e => { if(!isAct && !isMobile) e.currentTarget.style.background='#f0f4f0'; }}
                      onMouseLeave={e => { if(!isAct && !isMobile) e.currentTarget.style.background=isAct?'#fff8e1':i%2===0?'#fff':'#fafbf9'; }}>
                      <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                        <span style={{ fontSize:10, color:'#999', width:14, textAlign:'right' }}>{i+1}.</span>
                        <div style={{ width:9, height:9, borderRadius:2, background:data.fill, border:'1px solid rgba(0,0,0,.08)' }} />
                        <span style={{ fontWeight:500, color:'#1a2e1a' }}>{nm}</span>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        {viewMode==='opportunity'
                          ? <><span style={{ fontWeight:600, color:data.oppScore>40?'#b83d0a':'#666', fontSize:12 }}>{data.oppScore}</span><span style={{ fontSize:10, color:'#999', marginLeft:3 }}>{fmt$m(data.mktPremium)}</span></>
                          : <><span style={{ fontWeight:600, color:'#2d4a2d', fontSize:12 }}>{fmtPctM(data.share)}</span><span style={{ fontSize:10, color:'#999', marginLeft:3 }}>{fmt$m(data.mktPremium)}</span></>
                        }
                      </div>
                    </div>
                  );
                })}
                {(isYieldLayer ? Object.keys(yieldCountyData).length===0 : ranked.length===0) && <div style={{ padding:14, textAlign:'center', color:'#aaa', fontSize:11 }}>No data for this layer</div>}
              </div>
            </div>
          </div>
        )}

        {/* Mobile: show Rankings button if sidebar is hidden */}
        {isMobile && !showSidebar && !selected && (
          <button onClick={() => setShowSidebar(true)} style={{ width:'100%', padding:'10px', background:'#2d4a2d', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
            {viewMode==='opportunity' ? '🎯 View Opportunity Rankings' : '📊 View County Rankings'}
          </button>
        )}
      </div>
    </div>
  );
}

function DashboardScreen({ farmers, isMobile }) {
  const [drill, setDrill]         = useState(null);
  const [yearFilter, setYearFilter] = useState(String(CURRENT_CROP_YEAR));
  const [expandedHistCounty, setExpandedHistCounty] = useState(null);
  const [activeDashCrop, setActiveDashCrop] = useState(null);
  const [expandedDashCounty, setExpandedDashCounty] = useState(null);
  const [aipDrill, setAipDrill] = useState(null);
  const toggleDrill = (year, crop, aip) => {
    const k = `${year}||${crop}||${aip}`;
    setAipDrill(prev => prev === k ? null : k);
  };
  const isDrilled = (year, crop, aip) => aipDrill === `${year}||${crop}||${aip}`;
  const [historicalData, setHistoricalData] = useState([]);
  const [historicalYears, setHistoricalYears] = useState([]);

  // Load historical data on mount
  useEffect(() => {
    (async () => {
      try {
        const hist = await sbFetch("historical_summaries").catch(() => []);
        setHistoricalData(hist);
        const years = [...new Set(hist.map(h => h.crop_year).filter(Boolean))].sort((a,b) => b-a);
        setHistoricalYears(years);
      } catch(e) { console.warn("Failed to load historical data:", e); }
    })();
  }, []);

  // ── A YEAR THE BOOK HAS NOT ROLLED TO, THAT ALREADY CARRIES REAL COVERAGE ──
  // The lines of business do not roll together. LIVESTOCK rolls July 1 by rule
  // (LIVESTOCK_POLICY_TYPES), so CY2027 LRP and DRP are sold, premium-bearing and
  // reconcilable months before a single CY2027 acre is keyed — $205,574 of LRP on
  // 3 farms and $11,738 of DRP on 2, as of 2026-08-17.
  //
  // The year row could not offer that year, and the reason is structural rather
  // than an oversight: it is built from the farmer cards' `cropYear`, which is a
  // CROP display year, hard-capped at CURRENT_CROP_YEAR in the loader on purpose
  // (see clean.cropYear) so the forward-year APH shells cannot drag a whole farm
  // into a year holding no data. Livestock policies are explicitly barred from
  // setting it. So a correct rule for crops silently decided that sold livestock
  // did not exist, and the only trace of the roll on this page was a sentence in
  // the header banner with no number behind it.
  //
  // Derived from the ENDORSEMENTS' own cropYear — the authority for a livestock
  // year — and only where premium was actually charged, so a year with nothing
  // sold gets no button rather than an empty page. Presence test only; the
  // figures on screen come from the deduped rollups below (endorsements repeat
  // across crop-year policies — see drpEndKey).
  const forwardYears = (() => {
    const sold = new Set();
    farmers.forEach(f => {
      (f.lrpPolicies||[]).forEach(p => (p.endorsements||[]).forEach(e => {
        const y = Number(e.cropYear); const v = Number(e.premium) || Number(e.totalPremium) || 0;
        if (y > CURRENT_CROP_YEAR && v > 0) sold.add(y);
      }));
      (f.drpPolicies||[]).forEach(p => (p.endorsements||[]).forEach(e => {
        const y = Number(e.cropYear); const v = Number(e.totalPremium) || 0;
        if (y > CURRENT_CROP_YEAR && v > 0) sold.add(y);
      }));
    });
    return [...sold].sort((a,b) => b-a);
  })();
  const allYears = [...new Set([...forwardYears, ...farmers.map(f => f.cropYear).filter(Boolean), ...historicalYears])].sort((a,b) => b-a);
  const isHistoricalYear = historicalYears.includes(parseInt(yearFilter)) && parseInt(yearFilter) !== CURRENT_CROP_YEAR;
  // Ahead of the book: livestock is real, the crop side is shells. The LAYOUT is
  // the same one every other year renders — banner, stat boxes, sections — and
  // that is deliberate (Kevin, 2026-08-18: "I want all the boxes and everything to
  // show in 2027 even though most of them will be zero today"). It used to swap the
  // stat boxes for a bespoke panel; a later cut kept the boxes but dropped the ones
  // reading zero, and BOTH still read as a different screen beside CY2026. So this
  // flag no longer hides anything. What it decides now:
  //   • the crop ROSTER comes off the whole book, because `af` is empty on a
  //     forward year and an empty roster is what removed the boxes (cropRosterSource);
  //   • a crop card survives at 0 ac / $0 instead of being filtered out;
  //   • "no premium data" reads "no premium yet", because nothing is keyed rather
  //     than missing;
  //   • the crop-side panel prints UNDER the cards and COUNTS what has rolled, so
  //     a screen of zeros is explained by crop_coverage rather than left to guess.
  // Every NUMBER still goes through inDashYear() — the roster is a list of crops,
  // never a source of totals.
  const forwardYearView = !isHistoricalYear && forwardYears.includes(parseInt(yearFilter));
  const af = yearFilter === "all" ? farmers : farmers.filter(f => String(f.cropYear) === yearFilter);

  // ── YEAR SCOPE — the single gate every live rollup goes through ───────────
  // A farmer object merges ALL of their policy rows, so approvedYields,
  // scoEcoEndorsements, prfPolicies and lrpPolicies each carry EVERY crop year
  // at once. Anything summing them raw adds years together. That is what put
  // three different PRF figures on three screens, and it is the same shape as
  // the DRP dedup bug. Every live total below goes through inDashYear() so the
  // app rolls into the next crop year with no code change: when 2027 is keyed,
  // CURRENT_CROP_YEAR follows the premium and these filters follow it.
  const dashYear   = yearFilter === 'all' ? null : (parseInt(yearFilter) || CURRENT_CROP_YEAR);
  const inDashYear = (y) => dashYear == null || Number(y) === dashYear;

  // ── helpers ──
  // FALL crops = planted in fall, SOI exists by January. SPRING = planted May/June, SOI after July.
  // A farmer object merges approvedYields from ALL their policies. To avoid counting spring crop acres
  // that came from a wheat SOI (where the Summary of Coverage lists spring crops with carry-forward acres),
  // we only count spring crop acres for farmers who have NO wheat rows at all — meaning their only
  // policy is a dedicated spring crop policy.

  // WHEAT specifically — not every fall small grain. The guard exists because a
  // wheat SOI lists spring crops with carry-forward acres, so counting them would
  // double them; that reasoning is about WHEAT documents only. Testing the whole
  // FALL_CROPS set made a crop suppress ITSELF: a farmer holding triticale was a
  // "wheat farmer", so springSOIAcres returned 0 for their triticale and the crop
  // vanished from the dashboard — $2,113 (Austic $724, Meisenzahl $1,389), which
  // was exactly the gap against Book of Business by AIP. Barley and rye had the
  // same latent bug. Narrowing only ever adds crops back; it cannot drop any.
  const isWheatFarmer    = (f) => (f.approvedYields||[]).some(a => FALL_CROPS_SET.has((a.crop||"").toUpperCase()));
  // Only count SPRING acres for farmers with no wheat rows, because a wheat SOI
  // lists spring crops with carry-forward acres and counting them would double
  // them. The guard must NOT apply to a fall crop: barley, triticale and rye
  // belong on that same wheat SOI with real acres of their own, so suppressing
  // them deletes the crop outright. Triticale was missing from the dashboard
  // entirely — 265 ac / $2,113 (Austic $724, Meisenzahl $1,389) — which was the
  // whole remaining gap against Book of Business by AIP. Both growers really do
  // carry wheat, so narrowing what counts as a "wheat farmer" could never fix
  // it; the crop being asked about is what has to be tested.
  const springSOIAcres   = (f, crop) => (isWheatFarmer(f) && !FALL_CROPS_SET.has(crop)) ? 0 :
    (f.approvedYields||[]).filter(a=>
      a.crop===crop && inDashYear(a.cropYear) && a.plan && !a.plan.includes("ECO") && !a.plan.includes("SCO") && Number(a.acres)>0
    ).reduce((s,a)=>s+Number(a.acres),0);
  const soiPrem   = (f, crop) => (f.approvedYields||[]).filter(a=>a.crop===crop&&inDashYear(a.cropYear)&&a.plan&&!a.plan.includes("ECO")&&!a.plan.includes("SCO")).reduce((s,a)=>s+(Number(a.totalPremium)||0),0);
  const hasSoiAc  = (f, crop) => springSOIAcres(f, crop) > 0;

  // ── WHEAT ──
  const wheatFarmers = af.filter(f => (f.approvedYields||[]).some(a => a.crop==="WHEAT"));
  const wheatByCounty = {};
  // RMA county-level market data for 2026 Wheat (NY counties)
  const RMA_WHEAT_2026 = {
    ALLEGANY:    { policies: 9, acres: 1073, premium: 62241, liability: 308515 },
    CATTARAUGUS: { policies: 9, acres: 689, premium: 35557, liability: 217529 },
    CAYUGA:      { policies: 40, acres: 4252, premium: 145289, liability: 1414004 },
    CHAUTAUQUA:  { policies: 9, acres: 992, premium: 46187, liability: 262111 },
    COLUMBIA:    { policies: 5, acres: 417, premium: 24145, liability: 121480 },
    ERIE:        { policies: 18, acres: 1120, premium: 53784, liability: 323977 },
    GENESEE:     { policies: 37, acres: 4577, premium: 173581, liability: 1484740 },
    JEFFERSON:   { policies: 15, acres: 3398, premium: 124359, liability: 1034277 },
    LIVINGSTON:  { policies: 100, acres: 9575, premium: 497035, liability: 3425581 },
    MADISON:     { policies: 12, acres: 261, premium: 13254, liability: 68008 },
    MONROE:      { policies: 58, acres: 6943, premium: 321202, liability: 2091368 },
    NIAGARA:     { policies: 51, acres: 8268, premium: 422496, liability: 2719085 },
    ONEIDA:      { policies: 28, acres: 2208, premium: 97948, liability: 668085 },
    ONONDAGA:    { policies: 22, acres: 3101, premium: 104709, liability: 848164 },
    ONTARIO:     { policies: 55, acres: 5203, premium: 233684, liability: 1663560 },
    ORLEANS:     { policies: 38, acres: 4068, premium: 201050, liability: 1421647 },
    OSWEGO:      { policies: 1, acres: 27, premium: 504, liability: 5250 },
    SENECA:      { policies: 35, acres: 3595, premium: 184872, liability: 1272411 },
    STEUBEN:     { policies: 28, acres: 2535, premium: 108568, liability: 744323 },
    TOMPKINS:    { policies: 10, acres: 732, premium: 27872, liability: 251453 },
    WAYNE:       { policies: 16, acres: 1689, premium: 59699, liability: 476483 },
    WYOMING:     { policies: 13, acres: 1993, premium: 99328, liability: 693305 },
    YATES:       { policies: 27, acres: 2524, premium: 116275, liability: 952314 },
  };
  // RMA Summary of Business data by year → crop → county. Add new years here as files become available.
  const RMA_DATA = {
    '2025': {
      'CORN': {
        ALLEGANY: { policies:32, acres:10341, premium:412725 },
        CATTARAUGUS: { policies:34, acres:16049, premium:555766 },
        CAYUGA: { policies:108, acres:36125, premium:920890 },
        CHAUTAUQUA: { policies:21, acres:15700, premium:614393 },
        CLINTON: { policies:19, acres:23318, premium:289029 },
        COLUMBIA: { policies:19, acres:6245, premium:260902 },
        CORTLAND: { policies:25, acres:7746, premium:191615 },
        ERIE: { policies:57, acres:12584, premium:712648 },
        FRANKLIN: { policies:24, acres:16938, premium:187766 },
        GENESEE: { policies:100, acres:28649, premium:1114544 },
        JEFFERSON: { policies:51, acres:31481, premium:1230531 },
        LIVINGSTON: { policies:141, acres:51506, premium:2656540 },
        MADISON: { policies:28, acres:13105, premium:401679 },
        MONROE: { policies:95, acres:25665, premium:1706035 },
        MONTGOMERY: { policies:41, acres:11097, premium:495673 },
        NIAGARA: { policies:74, acres:30082, premium:2037581 },
        ONEIDA: { policies:48, acres:18595, premium:492942 },
        ONONDAGA: { policies:67, acres:25908, premium:690320 },
        ONTARIO: { policies:86, acres:26695, premium:1122297 },
        ORANGE: { policies:25, acres:2693, premium:114019 },
        ORLEANS: { policies:80, acres:36324, premium:1664077 },
        OSWEGO: { policies:17, acres:4400, premium:282081 },
        SENECA: { policies:56, acres:15569, premium:877712 },
        "ST. LAWRENCE": { policies:44, acres:30911, premium:886264 },
        STEUBEN: { policies:76, acres:22956, premium:1093268 },
        TOMPKINS: { policies:21, acres:4654, premium:157382 },
        ULSTER: { policies:11, acres:893, premium:73074 },
        WASHINGTON: { policies:33, acres:15723, premium:450321 },
        WAYNE: { policies:65, acres:17187, premium:571047 },
        WYOMING: { policies:55, acres:18726, premium:459144 },
        YATES: { policies:37, acres:6263, premium:308219 },
      },
      'SOYBEANS': {
        ALLEGANY: { policies:13, acres:1177, premium:23898 },
        CATTARAUGUS: { policies:17, acres:3566, premium:148429 },
        CAYUGA: { policies:86, acres:19354, premium:459104 },
        CHAUTAUQUA: { policies:9, acres:3203, premium:172501 },
        COLUMBIA: { policies:10, acres:2192, premium:90285 },
        CORTLAND: { policies:6, acres:727, premium:14233 },
        ERIE: { policies:41, acres:6808, premium:456296 },
        FRANKLIN: { policies:5, acres:1160, premium:46712 },
        GENESEE: { policies:70, acres:11659, premium:420974 },
        JEFFERSON: { policies:33, acres:9215, premium:520898 },
        LIVINGSTON: { policies:93, acres:15588, premium:788106 },
        MADISON: { policies:18, acres:2703, premium:90689 },
        MONROE: { policies:77, acres:13004, premium:625080 },
        MONTGOMERY: { policies:22, acres:3856, premium:147597 },
        NIAGARA: { policies:61, acres:20218, premium:933411 },
        ONEIDA: { policies:36, acres:6254, premium:160884 },
        ONONDAGA: { policies:41, acres:8676, premium:343708 },
        ONTARIO: { policies:78, acres:16642, premium:674080 },
        ORANGE: { policies:11, acres:1589, premium:66181 },
        ORLEANS: { policies:69, acres:21150, premium:600909 },
        OSWEGO: { policies:16, acres:4646, premium:178254 },
        SENECA: { policies:59, acres:17462, premium:590475 },
        "ST. LAWRENCE": { policies:31, acres:8480, premium:366666 },
        STEUBEN: { policies:25, acres:6234, premium:122378 },
        TOMPKINS: { policies:16, acres:2345, premium:78520 },
        WASHINGTON: { policies:4, acres:466, premium:16385 },
        WAYNE: { policies:68, acres:16263, premium:413931 },
        WYOMING: { policies:27, acres:1733, premium:91853 },
        YATES: { policies:32, acres:3605, premium:158665 },
      },
      'DRY BEANS': {
        CAYUGA: { policies:1, acres:85, premium:5031 },
        GENESEE: { policies:3, acres:466, premium:24993 },
        LIVINGSTON: { policies:15, acres:2925, premium:174373 },
        MONROE: { policies:7, acres:826, premium:64034 },
        ONTARIO: { policies:6, acres:839, premium:64128 },
        ORLEANS: { policies:3, acres:303, premium:12285 },
        YATES: { policies:1, acres:40, premium:3879 },
      },
      'POTATOES': {
        ERIE: { policies:3, acres:952, premium:47719 },
        GENESEE: { policies:3, acres:931, premium:47089 },
        LIVINGSTON: { policies:10, acres:1300, premium:223898 },
        MONROE: { policies:2, acres:86, premium:38815 },
        ORLEANS: { policies:2, acres:146, premium:44919 },
        STEUBEN: { policies:11, acres:1607, premium:646450 },
        WAYNE: { policies:6, acres:515, premium:175604 },
        WYOMING: { policies:4, acres:4005, premium:782078 },
      },
      'PASTURE,RANGELAND,FORAGE': {
        GENESEE: { policies:11, acres:3159, premium:147977 },
        LIVINGSTON: { policies:13, acres:2281, premium:99718 },
        MONROE: { policies:6, acres:546, premium:25881 },
        NIAGARA: { policies:5, acres:1896, premium:94648 },
        ONTARIO: { policies:7, acres:1066, premium:54550 },
        WYOMING: { policies:11, acres:2418, premium:118016 },
      },
      'MILK': {
        GENESEE:    { policies:7, lbs:62195000, premium:336462, liability:13925864, indemnity:315376 },
        LIVINGSTON: { policies:9, lbs:214150000, premium:1780349, liability:50308836, indemnity:1368300 },
        NIAGARA:    { policies:2, lbs:42102500, premium:270003, liability:10543863, indemnity:530654 },
        WYOMING:    { policies:6, lbs:184663750, premium:1037175, liability:34603201, indemnity:2036021 },
      },
      'FEEDER CATTLE': {
        GENESEE: { policies:1, head:0, premium:0 },
      },
    },
    '2026': {
      'WHEAT': {
        ALLEGANY: { policies:9, acres:1073, premium:62241 },
        CATTARAUGUS: { policies:9, acres:689, premium:35557 },
        CAYUGA: { policies:40, acres:4252, premium:145289 },
        CHAUTAUQUA: { policies:9, acres:992, premium:46187 },
        COLUMBIA: { policies:5, acres:417, premium:24145 },
        ERIE: { policies:18, acres:1120, premium:53784 },
        GENESEE: { policies:37, acres:4577, premium:173581 },
        JEFFERSON: { policies:15, acres:3398, premium:124359 },
        LIVINGSTON: { policies:100, acres:9575, premium:497035 },
        MADISON: { policies:12, acres:261, premium:13254 },
        MONROE: { policies:58, acres:6943, premium:321202 },
        NIAGARA: { policies:51, acres:8268, premium:422496 },
        ONEIDA: { policies:28, acres:2208, premium:97948 },
        ONONDAGA: { policies:22, acres:3101, premium:104709 },
        ONTARIO: { policies:55, acres:5203, premium:233684 },
        ORLEANS: { policies:38, acres:4068, premium:201050 },
        OSWEGO: { policies:1, acres:27, premium:504 },
        SENECA: { policies:35, acres:3595, premium:184872 },
        STEUBEN: { policies:28, acres:2535, premium:108568 },
        TOMPKINS: { policies:10, acres:732, premium:27872 },
        WAYNE: { policies:16, acres:1689, premium:59699 },
        WYOMING: { policies:13, acres:1993, premium:99328 },
        YATES: { policies:27, acres:2524, premium:116275 },
      },
      'PASTURE,RANGELAND,FORAGE': {
        ALLEGANY: { policies:7, acres:1720, premium:81485 },
        CATTARAUGUS: { policies:2, acres:3141, premium:133596 },
        CAYUGA: { policies:4, acres:625, premium:25958 },
        CHAUTAUQUA: { policies:6, acres:4055, premium:184822 },
        CHENANGO: { policies:1, acres:246, premium:10835 },
        COLUMBIA: { policies:1, acres:666, premium:4267 },
        CORTLAND: { policies:3, acres:1372, premium:61739 },
        ERIE: { policies:10, acres:5134, premium:247675 },
        ESSEX: { policies:1, acres:52, premium:447 },
        GENESEE: { policies:15, acres:3956, premium:185170 },
        HERKIMER: { policies:2, acres:536, premium:26751 },
        JEFFERSON: { policies:109, acres:31455, premium:1193924 },
        LEWIS: { policies:7, acres:1414, premium:39545 },
        LIVINGSTON: { policies:13, acres:3420, premium:171232 },
        MADISON: { policies:2, acres:1224, premium:51439 },
        MONROE: { policies:6, acres:631, premium:27878 },
        NIAGARA: { policies:8, acres:2653, premium:150162 },
        ONONDAGA: { policies:2, acres:247, premium:9254 },
        ONTARIO: { policies:6, acres:1217, premium:56591 },
        ORANGE: { policies:1, acres:152, premium:7414 },
        ORLEANS: { policies:2, acres:164, premium:9889 },
        OSWEGO: { policies:1, acres:96, premium:3306 },
        OTSEGO: { policies:3, acres:469, premium:20260 },
        RENSSELAER: { policies:1, acres:218, premium:11562 },
        SARATOGA: { policies:2, acres:494, premium:11853 },
        SCHOHARIE: { policies:1, acres:115, premium:6746 },
        SENECA: { policies:4, acres:290, premium:18279 },
        "ST. LAWRENCE": { policies:82, acres:34585, premium:1215973 },
        STEUBEN: { policies:10, acres:1873, premium:83558 },
        TIOGA: { policies:1, acres:1215, premium:64714 },
        TOMPKINS: { policies:1, acres:600, premium:16074 },
        WASHINGTON: { policies:1, acres:224, premium:4827 },
        WAYNE: { policies:4, acres:520, premium:29960 },
        WYOMING: { policies:14, acres:2635, premium:124248 },
        YATES: { policies:5, acres:1506, premium:99207 },
      },
      'MILK': {
        ALBANY: { policies:1, lbs:8000000, premium:23212, liability:1204933, indemnity:0 },
        BROOME: { policies:3, lbs:24300000, premium:187132, liability:5443796, indemnity:0 },
        CATTARAUGUS: { policies:1, lbs:828000, premium:6764, liability:149671, indemnity:0 },
        CAYUGA: { policies:13, lbs:485705702, premium:3408578, liability:89094708, indemnity:0 },
        CHAUTAUQUA: { policies:4, lbs:20093000, premium:155234, liability:4218494, indemnity:10907 },
        CHEMUNG: { policies:1, lbs:1500000, premium:11761, liability:239971, indemnity:0 },
        CHENANGO: { policies:1, lbs:9500000, premium:66884, liability:1638419, indemnity:0 },
        CLINTON: { policies:1, lbs:2400000, premium:13354, liability:398253, indemnity:0 },
        COLUMBIA: { policies:2, lbs:60000000, premium:415492, liability:10054451, indemnity:0 },
        CORTLAND: { policies:5, lbs:90700000, premium:565134, liability:15000248, indemnity:15617 },
        DELAWARE: { policies:4, lbs:4940000, premium:30917, liability:852438, indemnity:0 },
        FRANKLIN: { policies:2, lbs:42000000, premium:210233, liability:6663984, indemnity:0 },
        GENESEE: { policies:6, lbs:71430000, premium:405661, liability:13283814, indemnity:3464 },
        HERKIMER: { policies:1, lbs:500000, premium:2490, liability:91338, indemnity:0 },
        JEFFERSON: { policies:7, lbs:29950000, premium:204157, liability:5475703, indemnity:38162 },
        LEWIS: { policies:2, lbs:45200000, premium:150307, liability:7052839, indemnity:48040 },
        LIVINGSTON: { policies:9, lbs:159300000, premium:1416086, liability:35108547, indemnity:303334 },
        MADISON: { policies:2, lbs:10100000, premium:61123, liability:1789758, indemnity:0 },
        MONTGOMERY: { policies:2, lbs:5600000, premium:18314, liability:905984, indemnity:6863 },
        NIAGARA: { policies:2, lbs:63000000, premium:424599, liability:11977839, indemnity:79556 },
        "NEW YORK": { policies:1, lbs:12000000, premium:101074, liability:2674468, indemnity:0 },
        ONEIDA: { policies:5, lbs:28211335, premium:253204, liability:6531567, indemnity:0 },
        ONONDAGA: { policies:5, lbs:41866666, premium:285643, liability:7292269, indemnity:0 },
        ONTARIO: { policies:7, lbs:228333335, premium:1777800, liability:49780922, indemnity:404935 },
        OTSEGO: { policies:1, lbs:4000000, premium:20082, liability:689930, indemnity:5572 },
        SARATOGA: { policies:5, lbs:66504999, premium:579903, liability:14756310, indemnity:62506 },
        SCHOHARIE: { policies:1, lbs:1900000, premium:8086, liability:358293, indemnity:0 },
        SCHUYLER: { policies:3, lbs:29400000, premium:191164, liability:5594881, indemnity:267745 },
        "ST. LAWRENCE": { policies:8, lbs:249610000, premium:1942673, liability:51525322, indemnity:224684 },
        STEUBEN: { policies:2, lbs:13000000, premium:55213, liability:2396304, indemnity:0 },
        TOMPKINS: { policies:3, lbs:34750000, premium:203530, liability:5596232, indemnity:0 },
        WASHINGTON: { policies:6, lbs:163960000, premium:1166121, liability:28715429, indemnity:15442 },
        WAYNE: { policies:3, lbs:86620000, premium:550761, liability:16220817, indemnity:0 },
        WYOMING: { policies:7, lbs:175218752, premium:1040531, liability:28294948, indemnity:4292 },
      },
      'FEEDER CATTLE': {
        ALBANY: { policies:1, head:75, premium:8661, liability:121245 },
        BROOME: { policies:2, head:682, premium:59738, liability:890381 },
        CAYUGA: { policies:11, head:5477, premium:567254, liability:7453836 },
        CHAUTAUQUA: { policies:4, head:352, premium:34851, liability:523953 },
        CHEMUNG: { policies:1, head:60, premium:5245, liability:71074 },
        CLINTON: { policies:1, head:12, premium:736, liability:17057 },
        CORTLAND: { policies:6, head:780, premium:75876, liability:1085494 },
        FRANKLIN: { policies:4, head:2313, premium:145185, liability:2895380 },
        GENESEE: { policies:5, head:550, premium:58578, liability:844201 },
        HERKIMER: { policies:2, head:1232, premium:124959, liability:1701330 },
        JEFFERSON: { policies:7, head:2475, premium:239825, liability:3632529 },
        LIVINGSTON: { policies:10, head:2432, premium:195059, liability:3264874 },
        MADISON: { policies:1, head:90, premium:11440, liability:243743 },
        MONTGOMERY: { policies:1, head:225, premium:24039, liability:318894 },
        "NEW YORK": { policies:1, head:120, premium:4293, liability:149726 },
        NIAGARA: { policies:2, head:910, premium:47923, liability:1139331 },
        ONEIDA: { policies:2, head:180, premium:19282, liability:265036 },
        ONONDAGA: { policies:5, head:1209, premium:112649, liability:1654336 },
        ONTARIO: { policies:7, head:2167, premium:183233, liability:3061961 },
        SARATOGA: { policies:2, head:1086, premium:109065, liability:1470322 },
        SCHUYLER: { policies:1, head:50, premium:8864, liability:164829 },
        "ST. LAWRENCE": { policies:6, head:5880, premium:486701, liability:7811538 },
        STEUBEN: { policies:2, head:329, premium:34425, liability:534146 },
        TOMPKINS: { policies:1, head:19, premium:2069, liability:28434 },
        WASHINGTON: { policies:5, head:2118, premium:194345, liability:2892748 },
        WAYNE: { policies:3, head:1220, premium:109175, liability:1719997 },
        WYOMING: { policies:5, head:170, premium:15516, liability:231948 },
        YATES: { policies:1, head:180, premium:29159, liability:485975 },
      },
    },
  };
  // Build WHEAT_SHEET premium lookup: normalized_name|county → grossPremium
  const wsLookup = {};
  (typeof WHEAT_SHEET !== 'undefined' ? WHEAT_SHEET : []).forEach(sr => {
    const key = `${(sr.name||'').toUpperCase().replace(/\s+/g,'')}|${(sr.county||'').toUpperCase()}`;
    if (sr.grossPremium) wsLookup[key] = sr.grossPremium;
  });
  // Live wheat premium comes from the book and ONLY the book. There used to be a
  // fallback to WHEAT_SHEET — a per-farmer premium table hardcoded in this file —
  // whenever a row's stored premium was 0 or null. Plenty of wheat rows are
  // forward-year shells with null premium, so the sheet was quietly topping the
  // card up by $8,716 and making the Dashboard disagree with Book of Business by
  // AIP and Premium Analytics, which both read the table. A frozen constant
  // shadowing live data also can never roll into a new crop year. The constant is
  // left in place for the historical views that still reference it.
  const wheatPremForFarmer = (f, county) =>
    (f.approvedYields||[]).filter(a=>a.crop==="WHEAT"&&inDashYear(a.cropYear)&&a.plan&&!a.plan.includes("ECO")&&!a.plan.includes("SCO")&&(!county||((a.county||'').toUpperCase()===(county||'').toUpperCase()))).reduce((s,a)=>s+(Number(a.totalPremium)||0),0);
  wheatFarmers.forEach(f => {
    (f.approvedYields||[]).filter(a => a.crop==="WHEAT" && inDashYear(a.cropYear) && a.plan && !a.plan.includes("ECO") && !a.plan.includes("SCO")).forEach(a => {
      const c = (a.county||"Unknown").toUpperCase();
      if (!wheatByCounty[c]) wheatByCounty[c] = { acres:0, buGuar:0, premium:0, farmers:new Set() };
      wheatByCounty[c].acres  += Number(a.acres)||0;
      // PLANTED acres, not insured. `acres` here is INSURED and insured = planted + PP,
      // so the raw figure hands a full guarantee to acres that grew nothing. The row
      // carries ppAcres for exactly this reason — see its comment in loadData.
      wheatByCounty[c].buGuar += (a.acres && a.approvedYield && a.coverageLevel)
        ? a.approvedYield * a.coverageLevel * Math.max(0, Number(a.acres) - (Number(a.ppAcres) || 0))
        : 0;
      // Stored premium only — no WHEAT_SHEET fallback. See wheatPremForFarmer.
      wheatByCounty[c].premium += Number(a.totalPremium)||0;
      wheatByCounty[c].farmers.add(f.name);
    });
    // Add SCO/ECO premium from scoEcoEndorsements (no acres — they duplicate base plan acres)
    (f.scoEcoEndorsements||[]).filter(e => inDashYear(e.cropYear) && ((e.crop||'').toUpperCase() === 'WHEAT' || (e.crop||'').toUpperCase() === 'WINTER WHEAT')).forEach(e => {
      const c = (e.county||"Unknown").toUpperCase();
      if (!wheatByCounty[c]) wheatByCounty[c] = { acres:0, buGuar:0, premium:0, farmers:new Set() };
      wheatByCounty[c].premium += (Number(e.totalPremium)||0);
      wheatByCounty[c].farmers.add(f.name);
    });
  });
  // Total wheat premium helper: base plan + SCO/ECO
  const wheatAllPlansPrem = (f) => {
    const base = (f.approvedYields||[]).filter(a=>a.crop==="WHEAT"&&a.plan).reduce((s,a)=>s+(Number(a.totalPremium)||0),0);
    const scoEco = (f.scoEcoEndorsements||[]).filter(e=>(e.crop||'').toUpperCase()==='WHEAT'||(e.crop||'').toUpperCase()==='WINTER WHEAT').reduce((s,e)=>s+(Number(e.totalPremium)||0),0);
    return base + scoEco;
  };
  const wheatCountyRows  = Object.entries(wheatByCounty).map(([county,d])=>({ county, acres:d.acres, buGuar:d.buGuar, premium:d.premium, farmerCount:d.farmers.size, farmers:[...d.farmers], rma: RMA_WHEAT_2026[county]||null })).sort((a,b)=>b.acres-a.acres);
  const totalWheatAcres  = wheatCountyRows.reduce((s,r)=>s+r.acres,0);
  const totalWheatBuGuar = wheatCountyRows.reduce((s,r)=>s+r.buGuar,0);
  const totalWheatPrem   = wheatFarmers.reduce((s,f) => s + (wheatAllPlansPrem(f) || wheatPremForFarmer(f, null)), 0);
  // Wheat's total already includes SCO/ECO (wheatAllPlansPrem adds it); split it
  // so the card can show the same RP / SCO-ECO breakdown as every other crop.
  const totalWheatScoPrem = wheatFarmers.reduce((s,f) => s + (f.scoEcoEndorsements||[])
    .filter(e => ['WHEAT','WINTER WHEAT'].includes((e.crop||'').toUpperCase()))
    .reduce((ss,e) => ss + (Number(e.totalPremium)||0), 0), 0);
  const totalWheatRpPrem  = Math.max(0, totalWheatPrem - totalWheatScoPrem);
  const wheatWithPrem    = wheatFarmers.filter(f => wheatAllPlansPrem(f) > 0 || soiPrem(f,"WHEAT") > 0).length;
  const wheatMissingPrem = wheatFarmers.length - wheatWithPrem;

  // ── INDIVIDUAL SPRING CROPS — SOI acres only, dedicated spring policies only ──
  // Every crop the live book actually carries, not a fixed four. The historical
  // years render a card per crop straight from the snapshot (16 of them for
  // CY2025); this list was hardcoded to four, so CY2026 silently had no card at
  // all for grapes ($955k), potatoes ($313k), onions, cabbage, barley,
  // triticale or oats — roughly $1.4M of book invisible on the dashboard.
  // WHEAT is excluded because it has its own dedicated card above.
  const SPRING_BASE = ["CORN","SOYBEANS","DRY BEANS","PROCESSING BEANS"];
  // WHICH CROPS EXIST is a different question from WHAT EACH ONE HOLDS, and on a
  // year ahead of the book the two need different sources. `af` is EMPTY there by
  // construction — the farmer card's cropYear is a CROP display year the loader
  // caps at CURRENT_CROP_YEAR — so a roster built from it gives CY2027 no crops at
  // all and the year renders four cards against CY2026's sixteen. Kevin,
  // 2026-08-18: every box shows on every year, zeros included. So the ROSTER comes
  // off the whole book on a forward year while every NUMBER on the card still goes
  // through inDashYear() below. A crop the book carries in another year therefore
  // appears at 0 ac / $0 rather than being missing, which is the intent: barley
  // really does have CY2027 APH databases rolled.
  const cropRosterSource = forwardYearView ? farmers : af;
  const SPRING = [...new Set([
    ...SPRING_BASE,
    ...cropRosterSource.flatMap(f => (f.approvedYields || []).map(a => (a.crop || "").toUpperCase().trim())),
  ])].filter(c => c && c !== "WHEAT");
  const cropStats = {};
  SPRING.forEach(crop => {
    const farmers  = af.filter(f => hasSoiAc(f, crop));
    const acres    = farmers.reduce((s,f)=>s+springSOIAcres(f,crop),0);
    const prem     = farmers.reduce((s,f)=>s+soiPrem(f,crop),0);
    // soiPrem() is BASE PLANS ONLY — it filters out anything with SCO/ECO in the
    // plan name. A card built from it alone reported less than half of corn's
    // real premium for CY2026: $4,727,439 against $9,596,240, with $4,868,801 of
    // SCO/ECO/ARPI simply absent. Split it out the same way the 2025 view does
    // (which reads historical_summaries, where SCO-RP/ECO-RP are their own rows)
    // so both years show RP/APH, SCO/ECO and a real total.
    const scoPrem = farmers.reduce((s, f) => s + (f.scoEcoEndorsements || [])
      .filter(e => (e.crop || "").toUpperCase().trim() === crop && inDashYear(e.cropYear))
      .reduce((ss, e) => ss + (Number(e.totalPremium) || 0), 0), 0);
    cropStats[crop] = { farmers, acres, prem, rpPrem: prem, scoPrem, totalPrem: prem + scoPrem };
  });
  // For the collapsible Spring section table we still need county breakdown
  const springByCounty = {};
  SPRING.forEach(crop => {
    cropStats[crop].farmers.forEach(f => {
      (f.approvedYields||[]).filter(a=>a.crop===crop&&a.plan&&!a.plan.includes("ECO")&&!a.plan.includes("SCO")&&Number(a.acres)>0).forEach(a => {
        const c = a.county||"Unknown";
        if (!springByCounty[c]) springByCounty[c] = {};
        if (!springByCounty[c][crop]) springByCounty[c][crop] = { acres:0, farmers:new Set() };
        springByCounty[c][crop].acres += Number(a.acres);
        springByCounty[c][crop].farmers.add(f.name);
      });
    });
  });
  const springCountyRows = Object.entries(springByCounty).map(([county,crops])=>({ county, crops })).sort((a,b)=>Object.values(b.crops).reduce((s,c)=>s+c.acres,0)-Object.values(a.crops).reduce((s,c)=>s+c.acres,0));
  const springFarmers = af.filter(f => SPRING.some(crop => hasSoiAc(f, crop)));

  // ── DRP (cross-crop-year: DRP coverage is quarter-based, not crop-year-based) ──
  // DRP endorsements can span crop years (July 1 boundary), so we look at ALL farmers
  // regardless of the selected crop year filter, then filter by active/future end dates.
  // DRP dashboard TOTALS count only DRP we actually wrote (NAU / R&H). Prior-agent
  // DRP that we load purely for display — e.g. a transfer-in still carried on the
  // former agent's paper (aip like 'HUDSON') — must NOT inflate our book totals.
  // Strip those policies here so every downstream dashboard DRP aggregate (KPI card,
  // by-AIP, county drill, history) is book-only. The farmer still shows in the
  // Book list and on their own card, which read `farmers` directly, not this.
  const isBookAipPolicy = (p) => {
    const a = String(p?.aip || 'NAU').toUpperCase();
    return a === 'NAU' || a === 'RAIN_AND_HAIL';
  };
  const allDrpFarmers   = farmers
    .map(f => ({ ...f, drpPolicies: (f.drpPolicies || []).filter(isBookAipPolicy) }))
    .filter(f => (f.drpPolicies || []).length > 0);
  const drpFarmers      = allDrpFarmers; // alias for backward compat in rendering
  const todayDate = new Date(); todayDate.setHours(0,0,0,0);
  // LRP end dates are calendar days: read as local midnight, never UTC (see the PDF's LRP section).
  const lrpEndDay = (v) => { const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v || NaN); };
  const isActiveEnd = (e) => {
    if (!e.endDate) return true; // no end date = assume active
    const d = lrpEndDay(e.endDate); d.setHours(0,0,0,0);
    return d >= todayDate;
  };

  // ── DRP Dashboard: merge by farmer name, dedup across crop year policies ──
  // Dedup endorsements by drpEndKey — same endorsement can exist under both CY policies.
  // When duplicate exists under multiple crop years, assign the EARLIEST cropYear (original write).
  // Do NOT key on endDate/endNum here: neither is endorsement-specific, and doing
  // so silently ate the entire CY2026 DRP book. See drpEndKey.
  const _drpAllDeduped = (() => {
    const byName = {};
    allDrpFarmers.forEach(f => {
      const key = (f.name||'').trim().toLowerCase();
      if (!key) return;
      if (!byName[key]) byName[key] = { name: f.name, county: '', _endMap: {} };
      (f.drpPolicies||[]).forEach(p => {
        // Use DRP policy county (from policies table) — more accurate than farmer-level county
        // which may come from a crop policy in a different county
        if (p.county) byName[key].county = p.county;
        (p.endorsements||[]).forEach(e => {
          const dedupKey = drpEndKey(e);
          const existing = byName[key]._endMap[dedupKey];
          if (!existing || (e.cropYear && (!existing.cropYear || e.cropYear < existing.cropYear))) {
            byName[key]._endMap[dedupKey] = e;
          }
        });
      });
    });
    return Object.values(byName).map(r => ({
      name: r.name, county: r.county, allEnds: Object.values(r._endMap),
    }));
  })();

  // Filter to selected year for dashboard table + totals. Same gate as every
  // other live rollup — see dashYear/inDashYear above.
  const drpDashYear = dashYear;
  const drpDashMerged = _drpAllDeduped.map(r => {
    const ends = drpDashYear
      ? r.allEnds.filter(e => e.cropYear === drpDashYear)
      : r.allEnds; // "all years" shows everything
    return { ...r, ends };
  }).filter(r => r.ends.length > 0);

  // Active = current + future endorsements for selected year
  const activeDrpGross   = drpDashMerged.reduce((s,r)=>s+r.ends.filter(isActiveEnd).reduce((ss,e)=>ss+(Number(e.totalPremium)||0),0),0);
  const activeDrpPremium = drpDashMerged.reduce((s,r)=>s+r.ends.filter(isActiveEnd).reduce((ss,e)=>ss+(Number(e.producerPremium)||0),0),0);
  const activeDrpLiability = drpDashMerged.reduce((s,r)=>s+r.ends.filter(isActiveEnd).reduce((ss,e)=>ss+(Number(e.liability)||0),0),0);
  const activeDrpMilkLbs = drpDashMerged.reduce((s,r)=>s+r.ends.filter(isActiveEnd).reduce((ss,e)=>ss+(Number(e.declaredMilkProd)||0),0),0);
  // Year totals (for table footer)
  const totalDrpGross   = drpDashMerged.reduce((s,r)=>s+r.ends.reduce((ss,e)=>ss+(Number(e.totalPremium)||0),0),0);
  const totalDrpPremium = drpDashMerged.reduce((s,r)=>s+r.ends.reduce((ss,e)=>ss+(Number(e.producerPremium)||0),0),0);
  const totalDrpLiability = drpDashMerged.reduce((s,r)=>s+r.ends.reduce((ss,e)=>ss+(Number(e.liability)||0),0),0);
  const totalDrpMilkLbs = drpDashMerged.reduce((s,r)=>s+r.ends.reduce((ss,e)=>ss+(Number(e.declaredMilkProd)||0),0),0);

  // ── DRP by county (for market share drill-down) ──
  const drpByCounty = {};
  drpDashMerged.forEach(r => {
    const county = (r.county||'').toUpperCase().trim() || 'UNKNOWN';
    if (!drpByCounty[county]) drpByCounty[county] = { lbs: 0, gross: 0, farmers: {} };
    r.ends.forEach(e => {
      drpByCounty[county].lbs += Number(e.declaredMilkProd)||0;
      drpByCounty[county].gross += Number(e.totalPremium)||0;
    });
    const farmerGross = r.ends.reduce((s,e)=>s+(Number(e.totalPremium)||0),0);
    const farmerLbs = r.ends.reduce((s,e)=>s+(Number(e.declaredMilkProd)||0),0);
    if (farmerGross > 0) {
      if (!drpByCounty[county].farmers[r.name]) drpByCounty[county].farmers[r.name] = { name: r.name, acres: 0, gross: 0, lbs: 0 };
      drpByCounty[county].farmers[r.name].gross += farmerGross;
      drpByCounty[county].farmers[r.name].lbs += farmerLbs;
    }
  });

  // ── LRP ──
  // Book-only, same rule as DRP above: prior-agent LRP loaded for display (aip like
  // 'HUDSON') must NOT inflate our LRP dashboard totals / by-AIP / county rollups.
  // The farmer still appears in the dedicated LRP tab (rendered from `farmers`).
  // ── YEAR SCOPE for PRF / LRP ─────────────────────────────────────────────
  // A farmer object carries EVERY crop year of PRF and LRP at once (the loader
  // merges all their policy rows), so anything summing `f.prfPolicies` or
  // `p.endorsements` raw adds 2025 + 2026 + 2027 together. That is what put
  // three different PRF numbers on three screens: Premium Analytics scopes by
  // policies.crop_year and read the right $675,095, while this card and Book of
  // Business by AIP summed every year for $1,120,454. It only became visible
  // when CY2025 PRF was backfilled — before that there was barely a prior year
  // to add. DRP already did this correctly via drpDashMerged; PRF and LRP now
  // match it, through the shared inDashYear() gate defined above.
  // Source is `farmers`, NOT `af` — the same rule DRP already follows above, and
  // for the same reason. `af` narrows by the farmer CARD's cropYear, which is a
  // CROP display year: the loader explicitly bars livestock policies from setting
  // it and then caps it at CURRENT_CROP_YEAR. Filtering livestock through it asks
  // the crop book which year a livestock endorsement belongs to. That agreed with
  // the endorsement while every LRP card sat at the book year, and returns NOTHING
  // for CY2027 — precisely the year the endorsements are in. The endorsement's own
  // cropYear is the authority, and inDashYear() below is still the only gate.
  const lrpFarmers      = farmers
    .map(f => ({ ...f, lrpPolicies: (f.lrpPolicies||[]).filter(isBookAipPolicy)
      .map(p => ({ ...p, endorsements: (p.endorsements||[]).filter(e => inDashYear(e.cropYear)) }))
      .filter(p => (p.endorsements||[]).length > 0) }))
    .filter(f => (f.lrpPolicies||[]).length > 0);
  const activeLrpGross   = lrpFarmers.reduce((s,f)=>s+(f.lrpPolicies||[]).reduce((ss,p)=>ss+(p.endorsements||[]).filter(isActiveEnd).reduce((sss,e)=>sss+(Number(e.premium)||0),0),0),0);
  const activeLrpPremium = lrpFarmers.reduce((s,f)=>s+(f.lrpPolicies||[]).reduce((ss,p)=>ss+(p.endorsements||[]).filter(isActiveEnd).reduce((sss,e)=>sss+(Number(e.producerPremium)||0),0),0),0);
  const activeLrpHead    = lrpFarmers.reduce((s,f)=>s+(f.lrpPolicies||[]).reduce((ss,p)=>ss+(p.endorsements||[]).filter(isActiveEnd).reduce((sss,e)=>sss+(Number(e.head)||0),0),0),0);
  const totalLrpGross     = lrpFarmers.reduce((s,f)=>s+(f.lrpPolicies||[]).reduce((ss,p)=>ss+(p.endorsements||[]).reduce((sss,e)=>sss+(Number(e.premium)||0),0),0),0);
  const totalLrpPremium   = lrpFarmers.reduce((s,f)=>s+(f.lrpPolicies||[]).reduce((ss,p)=>ss+(p.endorsements||[]).reduce((sss,e)=>sss+(Number(e.producerPremium)||0),0),0),0);
  const totalLrpHead      = lrpFarmers.reduce((s,f)=>s+(f.lrpPolicies||[]).reduce((ss,p)=>ss+(p.endorsements||[]).reduce((sss,e)=>sss+(Number(e.head)||0),0),0),0);
  const totalLrpIndemnity = lrpFarmers.reduce((s,f)=>s+(f.lrpPolicies||[]).reduce((ss,p)=>ss+(p.endorsements||[]).reduce((sss,e)=>sss+(Number(e.payableIndemnity)||0),0),0),0);

  // ── LRP by county (for drill-down on the agency dashboard) ──
  const lrpByCounty = {};
  lrpFarmers.forEach(f => {
    (f.lrpPolicies||[]).forEach(pol => {
      const county = (pol.county||'').toUpperCase().trim() || 'UNKNOWN';
      if (!lrpByCounty[county]) lrpByCounty[county] = { head: 0, gross: 0, farmers: {} };
      (pol.endorsements||[]).forEach(e => {
        lrpByCounty[county].head += Number(e.head)||0;
        lrpByCounty[county].gross += Number(e.premium)||0;
      });
      const farmerGross = (pol.endorsements||[]).reduce((s,e)=>s+(Number(e.premium)||0),0);
      const farmerHead = (pol.endorsements||[]).reduce((s,e)=>s+(Number(e.head)||0),0);
      if (farmerGross > 0 || farmerHead > 0) {
        if (!lrpByCounty[county].farmers[f.name]) lrpByCounty[county].farmers[f.name] = { name: f.name, acres: 0, gross: 0, head: 0 };
        lrpByCounty[county].farmers[f.name].gross += farmerGross;
        lrpByCounty[county].farmers[f.name].head += farmerHead;
      }
    });
  });

  // ── LRP ENDING SOON ──
  const today60 = new Date(); today60.setHours(0,0,0,0);
  const cutoff60 = new Date(today60); cutoff60.setDate(cutoff60.getDate()+60);
  const lrpEndingSoon = [];
  lrpFarmers.forEach(f => {
    (f.lrpPolicies||[]).forEach(pol => {
      (pol.endorsements||[]).forEach(e => {
        if (!e.endDate) return;
        const d = lrpEndDay(e.endDate); if (isNaN(d)) return;
        d.setHours(0,0,0,0);
        const daysLeft = Math.round((d - today60) / 86400000);
        // Include: active within 60 days, OR already ended (negative daysLeft)
        if (daysLeft <= 60) {
          lrpEndingSoon.push({ farmerName: f.name, commodity: pol.commodity || e.commodity || "—", endDate: d, daysLeft, head: e.head||0, coveragePrice: e.coveragePrice||0, targetWeight: e.targetWeight||0, producerPremium: e.producerPremium||0, billingDate: e.billingDate||null, actualEndValue: e.actualEndValue||0, payableIndemnity: e.payableIndemnity||null });
        }
      });
    });
  });
  // Sort: active soonest-first (smallest daysLeft at top), then ended most-recently-ended first (least negative = closest to 0)
  lrpEndingSoon.sort((a,b) => {
    const aEnded = a.daysLeft < 0;
    const bEnded = b.daysLeft < 0;
    if (aEnded && !bEnded) return 1;   // ended goes below active
    if (!aEnded && bEnded) return -1;
    return a.daysLeft - b.daysLeft;    // ascending for both groups: soonest active first, most recently ended first
  });

  // ── PRF ──
  const prfFarmers      = af
    .map(f => ({ ...f, prfPolicies: (f.prfPolicies||[]).filter(p => inDashYear(p.cropYear)) }))
    .filter(f => (f.prfPolicies||[]).length > 0);
  const totalPrfGross   = prfFarmers.reduce((s,f)=>s+(f.prfPolicies||[]).reduce((ss,p)=>ss+(Number(p.totalPremium)||0),0),0);
  const totalPrfPremium = prfFarmers.reduce((s,f)=>s+(f.prfPolicies||[]).reduce((ss,p)=>ss+(Number(p.netPremium)||0),0),0);
  const totalPrfAcres   = prfFarmers.reduce((s,f)=>s+(f.prfPolicies||[]).reduce((ss,p)=>ss+(Number(p.totalAcres)||0),0),0);
  // PRF by county for drill-down — group by INTERVAL-level county (not policy-level)
  // so multi-county PRF policies (e.g. Livingston + Monroe) split correctly.
  const prfByCounty = {};
  prfFarmers.forEach(f => {
    const allIvs = (f.prfPolicies||[]).flatMap(p => (p.intervals||[]).map(iv => ({ ...iv, _pCounty: p.county })));
    // Group intervals by county, then by gridId to compute acres per grid (de-duped)
    const byCounty = {};
    allIvs.forEach(iv => {
      const c = (iv.county || iv._pCounty || 'Unknown').toUpperCase();
      if (!byCounty[c]) byCounty[c] = { grids:{}, premium:0 };
      const gid = iv.gridId || 'Unknown';
      if (!byCounty[c].grids[gid]) byCounty[c].grids[gid] = { acres:0, ivAcres:[] };
      byCounty[c].grids[gid].ivAcres.push(Number(iv.insuredAcres)||0);
      if (iv.totalGridAcres > 0) byCounty[c].grids[gid].acres = Number(iv.totalGridAcres);
      byCounty[c].premium += Number(iv.premium)||0;
    });
    Object.entries(byCounty).forEach(([c, data]) => {
      // Acres per grid = totalGridAcres if available, else max of insuredAcres in that grid (intervals share acreage)
      const acres = Object.values(data.grids).reduce((s,g) => s + (g.acres > 0 ? g.acres : Math.max(...g.ivAcres, 0)), 0);
      const net = data.premium;
      // Gross not available per-interval; approximate from policy-level subsidy ratio
      const totalSubsidy = (f.prfPolicies||[]).reduce((s,p) => s + (Number(p.subsidy)||0), 0);
      const totalNet     = (f.prfPolicies||[]).reduce((s,p) => s + (Number(p.netPremium)||0), 0);
      const subsidyRatio = totalNet > 0 ? totalSubsidy / totalNet : 0;
      const gross = net + (net * subsidyRatio);
      if (!prfByCounty[c]) prfByCounty[c] = { acres:0, gross:0, net:0, farmers:{} };
      prfByCounty[c].acres += acres;
      prfByCounty[c].gross += gross;
      prfByCounty[c].net   += net;
      if (!prfByCounty[c].farmers[f.name]) prfByCounty[c].farmers[f.name] = { name:f.name, acres:0, gross:0, net:0 };
      prfByCounty[c].farmers[f.name].acres += acres;
      prfByCounty[c].farmers[f.name].gross += gross;
      prfByCounty[c].farmers[f.name].net   += net;
    });
  });
  const prfCountyRows = Object.entries(prfByCounty).map(([county,d])=>({county,...d,farmers:d.farmers})).sort((a,b)=>b.acres-a.acres);

  // ── CSV EXPORT ──
  function exportCSV(rows, filename, cols) {
    const header = cols.map(c=>c.label).join(",");
    const body   = rows.map(r=>cols.map(c=>{ const v=c.get(r); return typeof v==="string"&&v.includes(",") ? `"${v}"` : v??""}).join(",")).join("\n");
    const blob=new Blob([header+"\n"+body],{type:"text/csv"}); const url=URL.createObjectURL(blob);
    const a=document.createElement("a"); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
  }
  const exportWheat  = () => exportCSV(wheatCountyRows,"wheat_by_county.csv",[{label:"County",get:r=>r.county},{label:"Farmers",get:r=>r.farmerCount},{label:"Insured Acres",get:r=>r.acres.toFixed(2)},{label:"Bu Guarantee",get:r=>Math.round(r.buGuar)}]);
  const exportFull   = () => {
    const rows=[];
    af.forEach(f=>{
      const rp=(f.approvedYields||[]).filter(a=>a.plan&&!a.plan.includes("ECO")&&!a.plan.includes("SCO"));
      if(rp.length===0){ rows.push({name:f.name,policy:f.policyNumber,yr:f.cropYear,crop:"—",county:"—",acres:"",aph:"",cov:"",bu:""}); return; }
      rp.forEach(r=>{
        // PLANTED acres. Measured 2026-08-21: across the 1,251 CY2026 base-plan rows this
        // export covers, 12,394 of 283,351 acres are prevent plant, so the insured figure
        // overstated the roster by 1,311,613 bu — 5.20%. PP acres carry their own reduced
        // guarantee and grow no crop.
        const plantedAc = Math.max(0, (Number(r.acres) || 0) - (Number(r.ppAcres) || 0));
        const bu = (r.acres && r.approvedYield && r.coverageLevel)
          ? Math.round(r.approvedYield * r.coverageLevel * plantedAc) : "";
        rows.push({name:f.name,policy:f.policyNumber,yr:f.cropYear,crop:r.crop,county:r.county||"",
                   acres:r.acres||"",aph:r.approvedYield||"",
                   // CAT is a 50% coverage level that reads identically to a 50% buy-up
                   // without the marker, and the guarantee behind it is ~45% lower.
                   cov:covLevelText(r.coverageLevel, r.coverageType),bu});
      });
    });
    exportCSV(rows,"full_roster.csv",[{label:"Farmer",get:r=>r.name},{label:"Policy",get:r=>r.policy||""},{label:"Crop Year",get:r=>r.yr||""},{label:"Crop",get:r=>r.crop},{label:"County",get:r=>r.county},{label:"Insured Acres",get:r=>r.acres},{label:"APH Yield",get:r=>r.aph},{label:"Coverage %",get:r=>r.cov},{label:"Bu Guarantee",get:r=>r.bu}]);
  };

  // ── shared table styles ──
  const TH = (left) => ({ padding:"8px 12px", textAlign:left?"left":"right", fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#9a7a40", textTransform:"uppercase", borderBottom:"1px solid #e8e0d0", background:"#f8f4ec" });
  const TD = (right) => ({ padding:"9px 12px", textAlign:right?"right":"left" });

  return (
    <div style={{ maxWidth:900, margin:"0 auto", padding: isMobile?"16px":"28px 36px" }}>

      {/* ── Header row ── */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:20 }}>
        <div>
          <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:"#60735f", textTransform:"uppercase", marginBottom:4 }}>Spartan Insurance Agency</div>
          <div style={{ fontSize:24, fontWeight:800, color:"#1a2e1a", fontFamily:"'Lora',serif" }}>Agency Dashboard</div>
          {/* ── Book year, stated ────────────────────────────────────────────
              CURRENT_CROP_YEAR follows the premium, so the book rolls by
              itself and nobody has to remember to bump it. The cost of that is
              that nothing ever ANNOUNCES the roll — and from July the files
              carry two crop years at once, which is how a -27 policy number
              turns up on a card showing CY2026 numbers and reads as a bug.
              Naming the year and what has already passed it costs one line.
              See summarizeForwardYear. */}
          {(() => {
            const fy = FORWARD_YEAR;
            if (!fy) return null;
            const rolled = [
              fy.fall   ? `${fy.crops.filter(c => FALL_CROPS_SET.has(c)).join(" + ").toLowerCase() || "fall crops"} on ${fy.farms} farm${fy.farms === 1 ? "" : "s"}` : null,
              // Livestock rolls on July 1 by rule, not by anything countable in
              // crop_coverage — state the rule rather than a number that would
              // have to come from a different table to be right.
              "livestock (rolls July 1)",
            ].filter(Boolean).join(", ");
            return (
              <div style={{ fontSize:11, color:"#60735f", marginTop:5, maxWidth:520, lineHeight:1.5 }}>
                Book year <b style={{ color:"#3a5a3a" }}>CY{CURRENT_CROP_YEAR}</b>
                {" — "}the crop year the book carries the most premium for.
                {" "}CY{fy.year} has started for {rolled}
                {fy.spring ? `, and ${fy.spring} spring row${fy.spring === 1 ? "" : "s"}` : ""}
                . {fy.spring
                    ? "Spring acreage has begun keying, so the book will roll on its own once CY" + fy.year + " carries more premium."
                    : "No CY" + fy.year + " acres are keyed yet, so spring crops and PRF are still CY" + CURRENT_CROP_YEAR + "."}
              </div>
            );
          })()}
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ fontSize:11, color:"#9a9a8a", fontWeight:700 }}>Year:</span>
          {/* "All Years" removed 2026-08-04 — it rendered the CURRENT year's
              banner and crop cards verbatim (isHistoricalYear is false for a
              non-numeric filter), so the only thing it changed was the by-AIP
              table stacking both sections. It read as a broken duplicate of
              2026. The `yearFilter === 'all'` branches further down are now
              unreachable; left in place rather than unpicked from the
              showHistorical/showingCurrentYear pair they're wired into. */}
          {allYears.map(String).map(y=>(
            <button key={y} onClick={()=>setYearFilter(y)} style={{ padding:"4px 12px", borderRadius:20, border:"1px solid #d4cfc8", fontSize:11, fontWeight:800, cursor:"pointer", background:yearFilter===y?"#1a2e1a":"#fff", color:yearFilter===y?"#fff":"#3a3a2a" }}>{y}</button>
          ))}
        </div>
      </div>

      {/* ── Historical Year View ── */}
      {isHistoricalYear && (() => {
        const hd = historicalData.filter(h => h.crop_year === parseInt(yearFilter));
        // Group by crop for summary
        const byCrop = {};
        hd.forEach(h => {
          const rawCrop = (h.crop || 'Unknown').toUpperCase().trim();
          const crop = (rawCrop === 'PRF' || rawCrop.startsWith('PASTURE')) ? 'PASTURE,RANGELAND,FORAGE' : rawCrop;
          const plan = h.type || 'RP';
          const isScoEco = plan === 'SCO-RP' || plan === 'ECO-RP';
          if (!byCrop[crop]) byCrop[crop] = { acres: 0, grossPrem: 0, netPrem: 0, rpPrem: 0, scoPrem: 0, otherPrem: 0, milkLbs: 0, farmers: new Set() };
          if (!isScoEco) byCrop[crop].acres += h.acres || 0;  // SCO/ECO share same physical acres as RP — don't double-count
          byCrop[crop].grossPrem += h.gross_premium || 0;
          byCrop[crop].netPrem += h.net_premium || 0;
          if (isScoEco) byCrop[crop].scoPrem += h.gross_premium || 0;
          else if (plan === 'RP' || plan === 'APH' || plan === 'YP') byCrop[crop].rpPrem += h.gross_premium || 0;
          else byCrop[crop].otherPrem += h.gross_premium || 0;
          byCrop[crop].farmers.add(h.farmer_name);
        });

        // Inject DRP from live farmer data for this historical crop year
        // (DRP spans crop years so it lives in live drpPolicies, not historical_summaries)
        const histCY = parseInt(yearFilter);
        const _histDrpCardMap = {};
        allDrpFarmers.forEach(f => {
          const nameKey = (f.name||'').trim().toLowerCase();
          if (!_histDrpCardMap[nameKey]) _histDrpCardMap[nameKey] = { name: f.name, endMap: {} };
          (f.drpPolicies||[]).forEach(p => {
            (p.endorsements||[]).forEach(e => {
              const dk = drpEndKey(e);
              const existing = _histDrpCardMap[nameKey].endMap[dk];
              if (!existing || (e.cropYear && (!existing.cropYear || e.cropYear < existing.cropYear))) {
                _histDrpCardMap[nameKey].endMap[dk] = e;
              }
            });
          });
        });
        Object.values(_histDrpCardMap).forEach(({ name, endMap }) => {
          Object.values(endMap).forEach(e => {
            if (e.cropYear === histCY) {
              if (!byCrop['MILK']) byCrop['MILK'] = { acres: 0, grossPrem: 0, netPrem: 0, rpPrem: 0, scoPrem: 0, otherPrem: 0, milkLbs: 0, farmers: new Set() };
              byCrop['MILK'].grossPrem += Number(e.totalPremium)||0;
              byCrop['MILK'].rpPrem += Number(e.totalPremium)||0;
              byCrop['MILK'].milkLbs += Number(e.declaredMilkProd)||0;
              byCrop['MILK'].farmers.add(name);
            }
          });
        });
        const totalAcres = hd.filter(h => { const p = h.type||'RP'; return p !== 'SCO-RP' && p !== 'ECO-RP'; }).reduce((s,h) => s + (h.acres||0), 0);
        const totalPrem = hd.reduce((s,h) => s + (h.gross_premium||0), 0) + (byCrop['MILK']?.grossPrem||0);
        const totalFarmers = new Set([...hd.map(h => h.farmer_name), ...(byCrop['MILK']?.farmers||[])]).size;
        // Shared with the live year via dashCropRank, so the boxes don't move
        // when you switch years.
        const sortedCrops = Object.keys(byCrop).sort((a,b) =>
          dashCropRank(a) - dashCropRank(b) || a.localeCompare(b));

        return (
          <div>
            <div style={{padding:'16px 20px',background:'#f8f0e0',borderRadius:10,marginBottom:20,border:'1px solid #e8d8b8'}}>
              <div style={{fontSize:12,fontWeight:700,color:'#8a6a2a',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:8}}>
                📜 Historical Data — {yearFilter}
              </div>
              <div style={{display:'flex',gap:24,flexWrap:'wrap',alignItems:'flex-end'}}>
                <div><div style={{fontSize:10,fontWeight:700,color:'#a08a4a',letterSpacing:'0.1em',textTransform:'uppercase'}}>Farmers</div><div style={{fontSize:22,fontWeight:800,color:'#1a2e1a'}}>{totalFarmers}</div></div>
                <div><div style={{fontSize:10,fontWeight:700,color:'#a08a4a',letterSpacing:'0.1em',textTransform:'uppercase'}}>Total Acres</div><div style={{fontSize:22,fontWeight:800,color:'#1a2e1a'}}>{fmt(totalAcres,0)}</div></div>
                <div style={{width:'1px',background:'#e8d8b8',alignSelf:'stretch',margin:'0 4px'}} />
                {(() => {
                  // DRP spans crop years, so it is NOT in historical_summaries — it's
                  // injected above from live drpPolicies into byCrop['MILK']. This
                  // total used to be rpTotal + scoTotal, both derived from `hd`
                  // alone, so it silently dropped the whole DRP book: 2025 read
                  // $15,383,347 against the Book of Business by AIP's $16,722,382,
                  // a $1,339,035 gap on the same screen. `totalPrem` twenty lines up
                  // already had it right and simply wasn't the one being rendered.
                  // Show DRP as its own stat so the three visibly add to the total.
                  const rpTotal  = hd.filter(h => !['SCO-RP','ECO-RP'].includes(h.type)).reduce((s,h) => s+(h.gross_premium||0), 0);
                  const scoTotal = hd.filter(h => h.type==='SCO-RP' || h.type==='ECO-RP').reduce((s,h) => s+(h.gross_premium||0), 0);
                  const drpTotal = byCrop['MILK']?.grossPrem || 0;
                  // Take the total from `totalPrem` rather than re-adding the parts,
                  // so anything injected there in future is carried automatically
                  // instead of quietly dropping out of the headline again.
                  const grandTotal = totalPrem;
                  return (<>
                    {rpTotal > 0 && <div><div style={{fontSize:10,fontWeight:700,color:'#a08a4a',letterSpacing:'0.1em',textTransform:'uppercase'}}>RP/APH Gross Premium</div><div style={{fontSize:20,fontWeight:800,color:'#2d4a2d'}}>{fmtD0(rpTotal)}</div></div>}
                    {scoTotal > 0 && <div><div style={{fontSize:10,fontWeight:700,color:'#a08a4a',letterSpacing:'0.1em',textTransform:'uppercase'}}>SCO/ECO Gross Premium</div><div style={{fontSize:20,fontWeight:800,color:'#6a5a2a'}}>{fmtD0(scoTotal)}</div></div>}
                    {drpTotal > 0 && <div><div style={{fontSize:10,fontWeight:700,color:'#a08a4a',letterSpacing:'0.1em',textTransform:'uppercase'}}>DRP Gross Premium</div><div style={{fontSize:20,fontWeight:800,color:'#3a5a7a'}}>{fmtD0(drpTotal)}</div></div>}
                    <div><div style={{fontSize:10,fontWeight:700,color:'#a08a4a',letterSpacing:'0.1em',textTransform:'uppercase'}}>Total Gross Premium</div><div style={{fontSize:22,fontWeight:800,color:'#1a2e1a'}}>{fmtD0(grandTotal)}</div></div>
                  </>);
                })()}
              </div>
            </div>

            {/* By Crop */}
            <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:20}}>
              {sortedCrops.map(crop => {
                const c = byCrop[crop];
                const LRP_CROPS = ['FEEDER CATTLE','FED CATTLE','LAMB','SWINE'];
                const DRP_CROPS = ['MILK'];
                return (
                  <div key={crop} onClick={() => { setDrill(drill === crop ? null : crop); setExpandedHistCounty(null); }}
                    style={{flex:'1 1 180px',padding:'14px 16px',background: drill===crop ? '#2d4a2d' : '#fff',borderRadius:8,
                      border:'1px solid #e4ece4',cursor:'pointer',transition:'all 0.15s',minWidth:160}}>
                    <div style={{fontSize:10,fontWeight:800,letterSpacing:'0.1em',textTransform:'uppercase',color:drill===crop?'#8ab87a':'#5a7a5a',marginBottom:4}}>
                      {crop === 'PASTURE,RANGELAND,FORAGE' ? 'PRF' : crop === 'MILK' ? 'DRP' : crop}
                    </div>
                    {DRP_CROPS.includes(crop) ? (
                      <>
                        {c.milkLbs > 0 && <div style={{fontSize:18,fontWeight:800,color:drill===crop?'#fff':'#1a2e1a'}}>{(c.milkLbs/1000000).toFixed(1)}M <span style={{fontSize:11,fontWeight:600,opacity:0.6}}>lbs</span></div>}
                        <div style={{fontSize:11,fontWeight:600,color:drill===crop?'#a0d0a0':'#60735f'}}>{c.farmers.size} farmers</div>
                        <div style={{fontSize:10,color:drill===crop?'#80b080':'#677167',lineHeight:1.6,marginTop:4}}>
                          <div style={{fontWeight:800,color:drill===crop?'#fff':'#1a2e1a'}}>Total {fmtD0(c.grossPrem)}</div>
                        </div>
                      </>
                    ) : LRP_CROPS.includes(crop) ? (
                      <>
                        <div style={{fontSize:18,fontWeight:800,color:drill===crop?'#fff':'#1a2e1a'}}>{fmtD0(c.grossPrem)}</div>
                        <div style={{fontSize:11,fontWeight:600,color:drill===crop?'#a0d0a0':'#60735f'}}>{c.farmers.size} farmers</div>
                      </>
                    ) : (
                      <>
                        <div style={{fontSize:18,fontWeight:800,color:drill===crop?'#fff':'#1a2e1a'}}>{fmt(c.acres,0)} <span style={{fontSize:11,fontWeight:600,opacity:0.6}}>ac</span></div>
                        <div style={{fontSize:11,fontWeight:600,color:drill===crop?'#a0d0a0':'#60735f',marginBottom:4}}>{c.farmers.size} farmers</div>
                        <div style={{fontSize:10,color:drill===crop?'#80b080':'#677167',lineHeight:1.6}}>
                          {c.rpPrem > 0 && <div>RP <span style={{fontWeight:700}}>{fmtD0(c.rpPrem)}</span></div>}
                          {c.scoPrem > 0 && <div>SCO/ECO <span style={{fontWeight:700}}>{fmtD0(c.scoPrem)}</span></div>}
                          <div style={{borderTop:`1px solid ${drill===crop?'#4a7a4a':'#e4ece4'}`,marginTop:2,paddingTop:2,fontWeight:800,color:drill===crop?'#fff':'#1a2e1a'}}>Total {fmtD0(c.grossPrem)}</div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Drill-down by county then farmer */}
            {drill && (() => {
              const isDRPHistDrill = drill === 'MILK';
              let cropRows = hd.filter(h => {
                const rawCrop = (h.crop||'').toUpperCase().trim();
                const normCropVal = (rawCrop === 'PRF' || rawCrop.startsWith('PASTURE')) ? 'PASTURE,RANGELAND,FORAGE' : rawCrop;
                return normCropVal === drill;
              });
              // RMA lookup for this crop+year from hardcoded constants
              const rmaYearData = RMA_DATA[String(yearFilter)] || {};
              const rmaCropData = rmaYearData[drill] || {};
              // Group by county
              const byCounty = {};

              // For DRP/MILK: build from live deduped DRP data instead of historical_summaries
              if (isDRPHistDrill) {
                const histCY = parseInt(yearFilter);
                const _drpHistCountyMap = {};
                allDrpFarmers.forEach(f => {
                  const nameKey = (f.name||'').trim().toLowerCase();
                  if (!_drpHistCountyMap[nameKey]) _drpHistCountyMap[nameKey] = { name: f.name, county: (f.county||'').toUpperCase(), endMap: {} };
                  (f.drpPolicies||[]).forEach(p => {
                    if (p.county && !_drpHistCountyMap[nameKey].county) _drpHistCountyMap[nameKey].county = p.county.toUpperCase();
                    (p.endorsements||[]).forEach(e => {
                      const dk = drpEndKey(e);
                      const existing = _drpHistCountyMap[nameKey].endMap[dk];
                      if (!existing || (e.cropYear && (!existing.cropYear || e.cropYear < existing.cropYear))) {
                        _drpHistCountyMap[nameKey].endMap[dk] = e;
                      }
                    });
                  });
                });
                Object.values(_drpHistCountyMap).forEach(({ name, county, endMap }) => {
                  const ends = Object.values(endMap).filter(e => e.cropYear === histCY);
                  if (ends.length === 0) return;
                  const c = county || 'UNKNOWN';
                  if (!byCounty[c]) byCounty[c] = { acres: 0, lbs: 0, grossPrem: 0, rpPrem: 0, scoPrem: 0, farmers: {} };
                  const totalPrem = ends.reduce((s,e) => s+(Number(e.totalPremium)||0), 0);
                  const totalLbs = ends.reduce((s,e) => s+(Number(e.declaredMilkProd)||0), 0);
                  byCounty[c].grossPrem += totalPrem;
                  byCounty[c].rpPrem += totalPrem;
                  byCounty[c].lbs += totalLbs;
                  const fKey = name;
                  if (!byCounty[c].farmers[fKey]) byCounty[c].farmers[fKey] = { farmer_name: name, practice: null, acres: 0, lbs: 0, rpPrem: 0, scoPrem: 0, totalPrem: 0 };
                  byCounty[c].farmers[fKey].totalPrem += totalPrem;
                  byCounty[c].farmers[fKey].rpPrem += totalPrem;
                  byCounty[c].farmers[fKey].lbs += totalLbs;
                });
              } else {
                // Standard: from historical_summaries
              cropRows.forEach(h => {
                const county = h.county || 'Unknown';
                if (!byCounty[county]) byCounty[county] = { acres: 0, grossPrem: 0, rpPrem: 0, scoPrem: 0, farmers: {} };
                byCounty[county].grossPrem += h.gross_premium || 0;
                const plan = h.type || 'RP';
                if (plan === 'SCO-RP' || plan === 'ECO-RP') byCounty[county].scoPrem += h.gross_premium || 0;
                else { byCounty[county].rpPrem += h.gross_premium || 0; byCounty[county].acres += h.acres || 0; }
                // PRF: group by name only — interval/practice splits not meaningful in summary view
                // All other crops: group by name+practice to preserve organic/irrigated distinctions
                const isPRFDrill = drill === 'PASTURE,RANGELAND,FORAGE';
                const fKey = isPRFDrill ? (h.farmer_name || 'Unknown') : `${h.farmer_name}|${h.practice||''}`;
                if (!byCounty[county].farmers[fKey]) byCounty[county].farmers[fKey] = { farmer_name: h.farmer_name, practice: isPRFDrill ? null : (h.practice||null), acres: 0, rpPrem: 0, scoPrem: 0, totalPrem: 0 };
                const ff = byCounty[county].farmers[fKey];
                ff.totalPrem += h.gross_premium || 0;
                if (plan === 'SCO-RP' || plan === 'ECO-RP') ff.scoPrem += h.gross_premium || 0;
                else { ff.rpPrem += h.gross_premium || 0; ff.acres += h.acres || 0; }
              });
              }
              const isLivestockDrill = !isDRPHistDrill && ['FEEDER CATTLE','FED CATTLE','LAMB','SWINE'].includes(drill);
              const cropLabel = drill === 'PASTURE,RANGELAND,FORAGE' ? 'PRF' : drill === 'MILK' ? 'DRP' : drill;
              // Build sorted county rows
              const countyRows = Object.entries(byCounty).map(([county, cd]) => {
                const rmaRow = rmaCropData[county.toUpperCase()] || null;
                return { county, ...cd, rma: rmaRow };
              }).sort((a,b) => b.grossPrem - a.grossPrem);
              const TH2 = (right) => ({ padding:'8px 12px', textAlign: right?'right':'left', fontSize:10, fontWeight:800, letterSpacing:'0.08em', color:'#5a7a5a', textTransform:'uppercase', borderBottom:'1px solid #e4ece4', whiteSpace:'nowrap' });
              const TD2 = (right) => ({ padding:'10px 12px', textAlign: right?'right':'left', borderBottom:'1px solid #f0ece6' });
              const colCount = isLivestockDrill ? 4 : 7;
              return (
                <Card>
                  <SectionTitle>📜 {cropLabel} — {yearFilter} Historical</SectionTitle>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                      <thead><tr style={{background:'#f8f8f4'}}>
                        <th style={TH2(false)}>County</th>
                        <th style={TH2(true)}>Farmers</th>
                        {!isLivestockDrill && <th style={TH2(true)}>{isDRPHistDrill ? 'Our Lbs' : 'Our Acres'}</th>}
                        {!isLivestockDrill && <th style={TH2(true)}>{isDRPHistDrill ? 'Mkt Lbs' : 'Mkt Acres'}</th>}
                        {!isLivestockDrill && <th style={TH2(true)}>{isDRPHistDrill ? 'Lbs Share' : 'Acre Share'}</th>}
                        <th style={TH2(true)}>RP/APH Prem</th>
                        <th style={TH2(true)}>SCO/ECO Prem</th>
                        <th style={TH2(true)}>Total Prem</th>
                      </tr></thead>
                      <tbody>
                        {countyRows.map((r, i) => {
                          const acShare = isDRPHistDrill
                            ? (r.rma && r.rma.lbs ? (r.lbs / r.rma.lbs * 100) : null)
                            : (!isLivestockDrill && r.rma && r.rma.acres) ? (r.acres / r.rma.acres * 100) : null;
                          const barColor = acShare != null ? `hsl(${Math.min(40 + acShare * 0.8, 120)},70%,45%)` : '#4a7a4a';
                          const farmerList = Object.values(r.farmers).sort((a,b) => b.totalPrem - a.totalPrem);
                          const isExpanded = expandedHistCounty === r.county;
                          return (
                            <React.Fragment key={i}>
                              <tr style={{borderBottom: isExpanded ? 'none' : '1px solid #f0ece6', cursor:'pointer', background: isExpanded ? '#f4faf4' : 'transparent'}}
                                onClick={() => setExpandedHistCounty(isExpanded ? null : r.county)}>
                                <td style={TD2(false)}>
                                  <span style={{fontWeight:800,color:'#1a2e1a',display:'flex',alignItems:'center',gap:6}}>
                                    <span style={{fontSize:10,color:'#7a9a7a',transition:'transform 0.15s',display:'inline-block',transform:isExpanded?'rotate(90deg)':'rotate(0deg)'}}>▶</span>
                                    {r.county}
                                  </span>
                                </td>
                                <td style={{...TD2(true),fontWeight:700,color:'#2d4a2d'}}>
                                  {farmerList.length}{r.rma && <span style={{fontWeight:400,color:'#677167',fontSize:10}}>/{r.rma.policies}</span>}
                                </td>
                                {!isLivestockDrill && <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",fontWeight:700}}>{isDRPHistDrill ? (r.lbs/1000000).toFixed(1)+'M' : fmt(r.acres,1)}</td>}
                                {!isLivestockDrill && <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",color:'#9a8060',fontSize:11}}>
                                  {isDRPHistDrill ? (r.rma && r.rma.lbs ? (r.rma.lbs/1000000).toFixed(1)+'M' : <span style={{color:'#ddd'}}>—</span>) : r.rma ? fmt(r.rma.acres,0) : <span style={{color:'#ddd'}}>—</span>}
                                </td>}
                                {!isLivestockDrill && <td style={TD2(true)}>
                                  {acShare != null ? (
                                    <div style={{display:'flex',alignItems:'center',gap:6,justifyContent:'flex-end'}}>
                                      <div style={{width:50,height:6,background:'#e8ece8',borderRadius:3,overflow:'hidden'}}>
                                        <div style={{width:`${Math.min(acShare,100)}%`,height:'100%',background:barColor,borderRadius:3}}/>
                                      </div>
                                      <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,fontSize:12,color:barColor}}>{acShare.toFixed(1)}%</span>
                                    </div>
                                  ) : <span style={{color:'#ddd',display:'block',textAlign:'right'}}>—</span>}
                                </td>}
                                <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",color:'#2d4a2d'}}>{r.rpPrem > 0 ? fmtD0(r.rpPrem) : <span style={{color:'#ddd'}}>—</span>}</td>
                                <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",color:'#6a5a2a'}}>{r.scoPrem > 0 ? fmtD0(r.scoPrem) : <span style={{color:'#ddd'}}>—</span>}</td>
                                <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",fontWeight:800,color:'#1a2e1a'}}>{fmtD0(r.grossPrem)}</td>
                              </tr>
                              {isExpanded && farmerList.map((f, fi) => (
                                <tr key={`f-${fi}`} style={{background:'#f7fbf7',borderBottom: fi===farmerList.length-1 ? '2px solid #d0e8d0' : '1px solid #eaf3ea'}}>
                                  <td style={{padding:'7px 12px 7px 32px',color:'#2d4a2d',fontWeight:600,fontSize:12}}>{f.farmer_name}</td>
                                  <td style={{padding:'7px 12px',textAlign:'right',fontSize:11,color:'#7a9a7a'}}></td>
                                  {!isLivestockDrill && <td style={{padding:'7px 12px',textAlign:'right',fontVariantNumeric: "tabular-nums",fontSize:12,color:'#2d4a2d'}}>{isDRPHistDrill ? (f.lbs>0?(f.lbs/1000000).toFixed(1)+'M':<span style={{color:'#ccc'}}>—</span>) : f.acres > 0 ? fmt(f.acres,1) : <span style={{color:'#ccc'}}>—</span>}</td>}
                                  {!isLivestockDrill && <td/>}
                                  {!isLivestockDrill && <td/>}
                                  <td style={{padding:'7px 12px',textAlign:'right',fontVariantNumeric: "tabular-nums",fontSize:12,color:'#2d4a2d'}}>{f.rpPrem > 0 ? fmtD0(f.rpPrem) : <span style={{color:'#ccc'}}>—</span>}</td>
                                  <td style={{padding:'7px 12px',textAlign:'right',fontVariantNumeric: "tabular-nums",fontSize:12,color:'#6a5a2a'}}>{f.scoPrem > 0 ? fmtD0(f.scoPrem) : <span style={{color:'#ccc'}}>—</span>}</td>
                                  <td style={{padding:'7px 12px',textAlign:'right',fontVariantNumeric: "tabular-nums",fontSize:12,fontWeight:700,color:'#1a2e1a'}}>{fmtD0(f.totalPrem)}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        {(() => {
                          const totalAc = isDRPHistDrill ? countyRows.reduce((s,r) => s+(r.lbs||0), 0) : countyRows.reduce((s,r) => s+(r.acres||0), 0);
                          const totalRmaAc = isDRPHistDrill
                            ? countyRows.filter(r=>r.rma&&r.rma.lbs).reduce((s,r) => s+(r.rma.lbs||0), 0)
                            : countyRows.filter(r=>r.rma).reduce((s,r) => s+(r.rma.acres||0), 0);
                          const totalShare = totalRmaAc > 0 ? (totalAc / totalRmaAc * 100) : null;
                          const totalRp = countyRows.reduce((s,r) => s+r.rpPrem, 0);
                          const totalSco = countyRows.reduce((s,r) => s+r.scoPrem, 0);
                          const totalGross = countyRows.reduce((s,r) => s+r.grossPrem, 0);
                          return (
                            <tr style={{background:'#f0f7e8',fontWeight:800}}>
                              <td colSpan={2} style={TD2(false)}><span style={{color:'#1a2e1a'}}>Total</span></td>
                              {!isLivestockDrill && <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",color:'#1a2e1a'}}>{isDRPHistDrill ? (totalAc/1000000).toFixed(1)+'M' : fmt(totalAc,1)}</td>}
                              {!isLivestockDrill && <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",color:'#9a8060',fontSize:11}}>{isDRPHistDrill ? (totalRmaAc>0?(totalRmaAc/1000000).toFixed(1)+'M':'') : totalRmaAc > 0 ? fmt(totalRmaAc,0) : ''}</td>}
                              {!isLivestockDrill && <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",fontWeight:800,color:'#2d4a2d'}}>{totalShare != null ? `${totalShare.toFixed(1)}%` : ''}</td>}
                              <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",color:'#2d4a2d'}}>{fmtD0(totalRp)}</td>
                              <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",color:'#6a5a2a'}}>{fmtD0(totalSco)}</td>
                              <td style={{...TD2(true),fontVariantNumeric: "tabular-nums",color:'#1a2e1a'}}>{fmtD0(totalGross)}</td>
                            </tr>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>
                </Card>
              );
            })()}
          </div>
        );
      })()}

      {/* ── Crop + Insurance stat boxes ──
          A YEAR AHEAD OF THE BOOK RENDERS THIS TOO. It used to be suppressed on
          a forward year in favour of a bespoke panel, which made CY2027 read as
          a different page rather than as a thin one — Kevin's call, 2026-08-18:
          keep the format identical, thin data is fine. The only concession is
          the empty-card filter on liveCards below. */}
      {!isHistoricalYear && (() => {
        const CROP_CARDS = [
          { key:'WHEAT',   label:'Wheat',       acres:totalWheatAcres,                    prem:totalWheatPrem, rpPrem:totalWheatRpPrem, scoPrem:totalWheatScoPrem,                           sub:`${wheatFarmers.length} policies`,                  bg:'#faf6ea', accent:'#a88000', border:'#d4b84066', textColor:'#3c2800' },
          // One card per crop the book actually carries, ordered the same way the
          // historical years order theirs, with anything unlisted alphabetical
          // after. Crops with neither acres nor premium are dropped so a stray
          // empty coverage row doesn't add a blank card — EXCEPT on a year ahead
          // of the book, where every crop is legitimately still at zero and
          // dropping them is what made CY2027 look like a different screen.
          ...(() => {
            // Order comes from the shared DASH_CROP_ORDER, applied once to the
            // whole card list below (including Wheat/PRF/DRP/LRP) rather than to
            // this slice alone — otherwise the specials always trail the crops
            // and can't sit in their shared positions.
            const titleCase = (c) => c.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
            return SPRING
              .filter(c => cropStats[c] && (forwardYearView || cropStats[c].acres > 0 || cropStats[c].totalPrem > 0))
              .map(c => ({
                key: c,
                label: c === 'PROCESSING BEANS' ? 'Proc. Beans' : titleCase(c),
                acres: cropStats[c].acres,
                prem: cropStats[c].totalPrem,
                rpPrem: cropStats[c].rpPrem,
                scoPrem: cropStats[c].scoPrem,
                sub: `${cropStats[c].farmers.length} policies`,
              }));
          })(),
          { key:'PRF',     label:'PRF',          acres:totalPrfAcres,                      prem:totalPrfGross,                            sub:`${prfFarmers.length} policies`,                    bg:'#eaf6fa', accent:'#0c6080', border:'#2890b866', textColor:'#042838' },
          // The year on these two labels is the SELECTED year, not the book year.
          // Livestock is the one thing that can be a year ahead of the book, so a
          // card reading "CY2026" over CY2027 endorsements is exactly backwards.
          { key:'DRP',     label:`DRP — CY${dashYear || CURRENT_CROP_YEAR}`, mainVal:(totalDrpMilkLbs/1000000).toFixed(1), mainUnit:'M lbs milk', acres:0, prem:totalDrpGross, sub:`${drpDashMerged.length} policies`, bg:'#e8f0fc', accent:'#0e38a0', border:'#2858c866', textColor:'#040e30' },
        ];
        const LIVESTOCK_CARDS = [
          { key:'LRP', label:`LRP — CY${dashYear || CURRENT_CROP_YEAR}`, mainVal:totalLrpHead, mainUnit:'head', prem:totalLrpGross, sub:`${lrpFarmers.length} policies`, bg:'#faf0e8', accent:'#8c3400', border:'#c8581866', textColor:'#3c1000' },
        ];

        // Drill-down data per crop
        const getDrillRows = (cropKey) => {
          if (cropKey === 'WHEAT') {
            // Build a WHEAT_SHEET lookup: normalized_name|county → grossPremium
            const wsLookup = {};
            (typeof WHEAT_SHEET !== 'undefined' ? WHEAT_SHEET : []).forEach(sr => {
              const key = `${sr.name.toUpperCase().replace(/\s+/g,'')}|${(sr.county||'').toUpperCase()}`;
              if (sr.grossPremium) wsLookup[key] = sr.grossPremium;
            });
            return wheatCountyRows.map(r => {
              // Deduplicate farmers by normalized name — sum acres/premium across variant spellings
              const farmerMap = {};
              r.farmers.forEach(name => {
                const f = wheatFarmers.find(wf=>wf.name===name);
                const ays = (f?.approvedYields||[]).filter(a=>a.crop==='WHEAT'&&inDashYear(a.cropYear)&&(a.county||'').toUpperCase()===r.county.toUpperCase()&&a.plan&&!a.plan.includes('ECO')&&!a.plan.includes('SCO'));
                const acres = ays.reduce((s,a)=>s+(Number(a.acres)||0),0);
                let gross = ays.reduce((s,a)=>s+(Number(a.totalPremium)||0),0);
                // Add SCO/ECO premium from scoEcoEndorsements
                const scoEcoPrem = (f?.scoEcoEndorsements||[]).filter(e=>inDashYear(e.cropYear)&&(e.crop||'').toUpperCase()==='WHEAT'&&(e.county||'').toUpperCase()===r.county.toUpperCase()).reduce((s,e)=>s+(Number(e.totalPremium)||0),0);
                gross += scoEcoPrem;
                // No WHEAT_SHEET fallback — the drill has to add up to the card it
                // opened from, and the card now reads stored premium only.
                // Normalize name for dedup: strip parenthetical suffixes like "(ARBOR FARMS)"
                const normName = name.replace(/\s*\(.*?\)\s*$/, '').trim();
                if (!farmerMap[normName]) farmerMap[normName] = { name: normName, acres: 0, gross: 0 };
                farmerMap[normName].acres += acres;
                farmerMap[normName].gross += gross;
              });
              return {
                county: r.county, acres: r.acres, gross: r.premium, net: null,
                rmaAcres: r.rma?.acres||null, rmaPolicies: r.rma?.policies||null,
                buGuar: r.buGuar,
                farmers: Object.values(farmerMap).sort((a,b)=>b.acres-a.acres || b.gross-a.gross),
              };
            });
          }
          if (cropKey === 'PRF') {
            const prfRmaData = (RMA_DATA[String(yearFilter)]||{})['PASTURE,RANGELAND,FORAGE']||{};
            return prfCountyRows.map(r => {
              const rmaC = prfRmaData[r.county.toUpperCase()]||null;
              return {
                county: r.county, acres: r.acres, gross: r.gross, net: r.net,
                rmaAcres: rmaC?.acres||null, rmaPolicies: rmaC?.policies||null, buGuar: null,
                farmers: Object.values(r.farmers).sort((a,b)=>b.gross-a.gross).map(f=>({name:f.name,acres:f.acres,gross:f.gross})),
              };
            });          }
          // DRP — by county with lbs as the share metric
          if (cropKey === 'DRP') {
            const rmaData = (RMA_DATA[String(drpDashYear||CURRENT_CROP_YEAR)]||{})['MILK']||{};
            return Object.entries(drpByCounty).map(([county,d]) => {
              const rmaC = rmaData[county]||null;
              return {
                county, acres: d.lbs, gross: d.gross, net: null,
                rmaAcres: rmaC?.lbs||null, rmaPolicies: rmaC?.policies||null, buGuar: null,
                isDRP: true,
                farmers: Object.values(d.farmers).sort((a,b)=>b.gross-a.gross),
              };
            }).sort((a,b)=>b.gross-a.gross);
          }
          // LRP — by county with head as the share metric
          if (cropKey === 'LRP') {
            return Object.entries(lrpByCounty).map(([county,d]) => ({
              county, acres: d.head, gross: d.gross, net: null,
              rmaAcres: null, rmaPolicies: null, buGuar: null,
              isLRP: true,
              farmers: Object.values(d.farmers).sort((a,b)=>b.gross-a.gross),
            })).sort((a,b)=>b.gross-a.gross);
          }
          // Spring crops — per county for this specific crop
          if (!cropStats[cropKey]) return [];
          const byCounty = {};
          (cropStats[cropKey].farmers||[]).forEach(f => {
            (f.approvedYields||[]).filter(a=>a.crop===cropKey&&a.plan&&!a.plan.includes('ECO')&&!a.plan.includes('SCO')&&Number(a.acres)>0).forEach(a => {
              const c = (a.county||'Unknown').toUpperCase();
              if (!byCounty[c]) byCounty[c] = { acres:0, gross:0, farmers:{} };
              byCounty[c].acres += Number(a.acres)||0;
              byCounty[c].gross += Number(a.totalPremium)||0;
              if (!byCounty[c].farmers[f.name]) byCounty[c].farmers[f.name] = { name:f.name, acres:0, gross:0 };
              byCounty[c].farmers[f.name].acres += Number(a.acres)||0;
              byCounty[c].farmers[f.name].gross += Number(a.totalPremium)||0;
            });
          });
          const rmaKey = cropKey === 'DRY BEANS' ? 'DRY BEANS' : cropKey === 'PROCESSING BEANS' ? 'PROCESSING BEANS' : cropKey;
          return Object.entries(byCounty).map(([county,d]) => {
            const rmaRow = (RMA_DATA[String(CURRENT_CROP_YEAR)]||{})[rmaKey]||{};
            const rmaC = rmaRow[county]||null;
            return { county, acres:d.acres, gross:d.gross, net:null, rmaAcres:rmaC?.acres||null, rmaPolicies:rmaC?.policies||null, buGuar:null, farmers:Object.values(d.farmers).sort((a,b)=>b.gross-a.gross) };
          }).sort((a,b)=>b.acres-a.acres);
        };

        const drillRows = activeDashCrop ? getDrillRows(activeDashCrop) : [];
        const TH3 = (r) => ({ padding:'8px 12px', textAlign:r?'right':'left', fontSize:10, fontWeight:800, letterSpacing:'0.08em', color:'#5a7a5a', textTransform:'uppercase', borderBottom:'1px solid #e4ece4', whiteSpace:'nowrap' });
        const TD3 = (r) => ({ padding:'9px 12px', textAlign:r?'right':'left', borderBottom:'1px solid #f0ece6' });
        const isPRFDrill = activeDashCrop === 'PRF';
        const isDRPDrill = activeDashCrop === 'DRP';
        const isLRPDrill = activeDashCrop === 'LRP';

        // Same summary strip the historical years carry, built from live data so
        // the two layouts read identically. Farmers is a DISTINCT-name count, not
        // a policy count — a grower with three policies is one farmer, which is
        // how the 2025 banner counts and the only way the two years compare.
        // One list, one order — crops and the livestock/PRF/DRP specials sorted
        // together by the shared rank so each lands in the same slot as it does
        // on the historical years.
        // Every card the book carries, on every year, in one shared order. An
        // earlier cut of this dropped the zero cards on a forward year; Kevin
        // looked at CY2027 beside CY2026 and the two did not read as the same
        // screen, which was the whole point of the change. A zero on a year that
        // has not been keyed is information, not breakage — the crop-side panel
        // under the cards is what says WHY, counted off crop_coverage.
        const liveCards = [...CROP_CARDS, ...LIVESTOCK_CARDS]
          .sort((a, b) => dashCropRank(a.key) - dashCropRank(b.key) || String(a.key).localeCompare(String(b.key)));
        const bannerFarmers = new Set([
          ...CROP_CARDS.flatMap(c => (cropStats[c.key]?.farmers || []).map(f => f.name)),
          ...wheatFarmers.map(f => f.name), ...prfFarmers.map(f => f.name),
          ...lrpFarmers.map(f => f.name), ...drpDashMerged.map(r => r.name),
        ].filter(Boolean)).size;
        const bannerAcres = liveCards.reduce((s, c) => s + (Number(c.acres) || 0), 0);
        const bannerRp    = liveCards.reduce((s, c) => s + (Number(c.rpPrem) || 0), 0);
        const bannerSco   = liveCards.reduce((s, c) => s + (Number(c.scoPrem) || 0), 0);
        // PRF/LRP/DRP carry no RP/SCO split, so add them straight into the total
        // rather than letting the two component lines silently drop them.
        const bannerTotal = liveCards.reduce((s, c) => s + (Number(c.prem) || 0), 0);

        return (
          <div style={{marginBottom:18}}>
            <div style={{padding:'16px 20px',background:'#eef6ea',borderRadius:10,marginBottom:20,border:'1px solid #cfe6cf'}}>
              <div style={{fontSize:12,fontWeight:700,color:'#3a6a2a',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:8}}>
                🌱 Live Book — {dashYear || CURRENT_CROP_YEAR}
              </div>
              <div style={{display:'flex',gap:24,flexWrap:'wrap',alignItems:'flex-end'}}>
                <div><div style={{fontSize:10,fontWeight:700,color:'#6a9a5a',letterSpacing:'0.1em',textTransform:'uppercase'}}>Farmers</div><div style={{fontSize:22,fontWeight:800,color:'#1a2e1a'}}>{bannerFarmers}</div></div>
                <div><div style={{fontSize:10,fontWeight:700,color:'#6a9a5a',letterSpacing:'0.1em',textTransform:'uppercase'}}>Total Acres</div><div style={{fontSize:22,fontWeight:800,color:'#1a2e1a'}}>{fmt(bannerAcres,0)}</div></div>
                {/* The divider only means something on one row; once the figures wrap on a phone it is a stray line. */}
                {!isMobile && <div style={{width:'1px',background:'#cfe6cf',alignSelf:'stretch',margin:'0 4px'}} />}
                {bannerRp > 0 && <div><div style={{fontSize:10,fontWeight:700,color:'#6a9a5a',letterSpacing:'0.1em',textTransform:'uppercase'}}>RP/APH Gross Premium</div><div style={{fontSize:20,fontWeight:800,color:'#2d4a2d'}}>{fmtD0(bannerRp)}</div></div>}
                {bannerSco > 0 && <div><div style={{fontSize:10,fontWeight:700,color:'#6a9a5a',letterSpacing:'0.1em',textTransform:'uppercase'}}>SCO/ECO Gross Premium</div><div style={{fontSize:20,fontWeight:800,color:'#6a5a2a'}}>{fmtD0(bannerSco)}</div></div>}
                {totalDrpGross > 0 && <div><div style={{fontSize:10,fontWeight:700,color:'#6a9a5a',letterSpacing:'0.1em',textTransform:'uppercase'}}>DRP Gross Premium</div><div style={{fontSize:20,fontWeight:800,color:'#3a5a7a'}}>{fmtD0(totalDrpGross)}</div></div>}
                <div><div style={{fontSize:10,fontWeight:700,color:'#6a9a5a',letterSpacing:'0.1em',textTransform:'uppercase'}}>Total Gross Premium</div><div style={{fontSize:22,fontWeight:800,color:'#1a2e1a'}}>{fmtD0(bannerTotal)}</div></div>
              </div>
            </div>
            {/* Crop cards */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:10 }}>
              {liveCards.map(c => {
                const isActive = activeDashCrop === c.key;
                return (
                  <div key={c.key} onClick={() => { setActiveDashCrop(isActive ? null : c.key); setExpandedDashCounty(null); }}
                    style={{ flex:'1 1 180px', padding:'14px 16px', background: isActive ? '#2d4a2d' : '#fff', borderRadius:8,
                      border:'1px solid #e4ece4', cursor:'pointer', transition:'all 0.15s', minWidth:160 }}>
                    <div style={{ fontSize:10, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color: isActive ? '#8ab87a' : '#5a7a5a', marginBottom:4 }}>
                      {c.label} {isActive ? '▾' : ''}
                    </div>
                    {c.mainVal != null ? (
                      <>
                        <div style={{fontSize:18,fontWeight:800,color:isActive?'#fff':'#1a2e1a'}}>{c.mainVal} <span style={{fontSize:11,fontWeight:600,opacity:0.6}}>{c.mainUnit}</span></div>
                        <div style={{fontSize:11,fontWeight:600,color:isActive?'#a0d0a0':'#60735f'}}>{c.sub}</div>
                        <div style={{fontSize:10,color:isActive?'#80b080':'#677167',lineHeight:1.6,marginTop:4}}>
                          <div style={{fontWeight:800,color:isActive?'#fff':'#1a2e1a'}}>Total {fmtD0(c.prem)}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{fontSize:18,fontWeight:800,color:isActive?'#fff':'#1a2e1a'}}>{c.acres > 0 ? fmt(c.acres,0) : <span style={{opacity:0.3}}>0</span>} <span style={{fontSize:11,fontWeight:600,opacity:0.6}}>ac</span></div>
                        <div style={{fontSize:11,fontWeight:600,color:isActive?'#a0d0a0':'#60735f',marginBottom:4}}>{c.sub}</div>
                        {c.prem > 0
                          ? <div style={{fontSize:10,color:isActive?'#80b080':'#677167',lineHeight:1.6}}>
                              {/* Same RP / SCO-ECO / Total breakdown the historical
                                  years show, so the two layouts read identically. */}
                              {c.rpPrem  > 0 && <div>RP {fmtD0(c.rpPrem)}</div>}
                              {c.scoPrem > 0 && <div>SCO/ECO {fmtD0(c.scoPrem)}</div>}
                              <div style={{fontWeight:800,color:isActive?'#fff':'#1a2e1a',borderTop:(c.rpPrem>0||c.scoPrem>0)?`1px solid ${isActive?'#4a7a4a':'#e4ece4'}`:'none',paddingTop:(c.rpPrem>0||c.scoPrem>0)?3:0,marginTop:(c.rpPrem>0||c.scoPrem>0)?3:0}}>Total {fmtD0(c.prem)}</div>
                            </div>
                          : <div style={{fontSize:11,color:'#aaa',marginTop:3}}>{forwardYearView ? 'no premium yet' : 'no premium data'}</div>}
                      </>
                    )}
                  </div>
                );
              })}
              {/* LRP now lives in liveCards above, ordered with everything else. */}
            </div>

            {/* Inline drill-down */}
            {activeDashCrop && drillRows.length > 0 && (
              <div style={{background:'#fff',border:'1px solid #e4ece4',borderRadius:10,overflow:'hidden',marginTop:4}}>
                <div style={{padding:'12px 16px',background:'#f4faf4',borderBottom:'1px solid #e4ece4',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontWeight:800,fontSize:14,color:'#1a2e1a'}}>{activeDashCrop === 'PRF' ? 'PRF — Pasture, Rangeland & Forage' : activeDashCrop === 'DRP' ? 'DRP — Dairy Revenue Protection' : activeDashCrop === 'LRP' ? 'LRP — Livestock Risk Protection' : activeDashCrop === 'PROCESSING BEANS' ? 'Processing Beans' : activeDashCrop.charAt(0)+activeDashCrop.slice(1).toLowerCase()} — By County</span>
                  <button onClick={()=>{setActiveDashCrop(null);setExpandedDashCounty(null);}} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#9a9a8a',lineHeight:1}}>×</button>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                    <thead><tr style={{background:'#f8f8f4'}}>
                      <th style={TH3(false)}>County</th>
                      <th style={TH3(true)}>Farmers</th>
                      <th style={TH3(true)}>{isDRPDrill ? 'Our Lbs' : isLRPDrill ? 'Our Head' : 'Our Acres'}</th>
                      {drillRows.some(r=>r.rmaAcres) && <th style={TH3(true)}>{isDRPDrill ? 'Mkt Lbs' : isLRPDrill ? 'Mkt Head' : 'Mkt Acres'}</th>}
                      {drillRows.some(r=>r.rmaAcres) && <th style={TH3(true)}>{isDRPDrill ? 'Lbs Share' : isLRPDrill ? 'Head Share' : 'Acre Share'}</th>}
                      {drillRows.some(r=>r.buGuar) && <th style={TH3(true)}>Bu. Guarantee</th>}
                      <th style={TH3(true)}>{isPRFDrill ? 'Net Premium' : 'Gross Premium'}</th>
                    </tr></thead>
                    <tbody>
                      {drillRows.map((r,i) => {
                        const acShare = r.rmaAcres ? (r.acres/r.rmaAcres*100) : null;
                        const barColor = acShare != null ? `hsl(${Math.min(40+acShare*0.8,120)},70%,45%)` : '#4a7a4a';
                        const isExp = expandedDashCounty === r.county;
                        const hasRma = drillRows.some(rr=>rr.rmaAcres);
                        const hasBu = drillRows.some(rr=>rr.buGuar);
                        const colSpanTotal = 3 + (hasRma?2:0) + (hasBu?1:0) + 1;
                        return (
                          <React.Fragment key={i}>
                            <tr style={{borderBottom: isExp?'none':'1px solid #f0ece6', cursor:'pointer', background:isExp?'#f4faf4':'transparent'}}
                              onClick={()=>setExpandedDashCounty(isExp?null:r.county)}>
                              <td style={TD3(false)}>
                                <span style={{fontWeight:800,color:'#1a2e1a',display:'flex',alignItems:'center',gap:6}}>
                                  <span style={{fontSize:10,color:'#7a9a7a',display:'inline-block',transform:isExp?'rotate(90deg)':'rotate(0deg)',transition:'transform 0.15s'}}>▶</span>
                                  {r.county}
                                </span>
                              </td>
                              <td style={{...TD3(true),fontWeight:700,color:'#2d4a2d'}}>
                                {r.farmers.length}{r.rmaPolicies && <span style={{fontWeight:400,color:'#677167',fontSize:10}}>/{r.rmaPolicies}</span>}
                              </td>
                              <td style={{...TD3(true),fontVariantNumeric: "tabular-nums",fontWeight:700}}>{isDRPDrill ? (r.acres/1000000).toFixed(1)+'M' : isLRPDrill ? fmt(r.acres,0) : fmt(r.acres,1)}</td>
                              {hasRma && <td style={{...TD3(true),fontVariantNumeric: "tabular-nums",color:'#9a8060',fontSize:11}}>{r.rmaAcres ? (isDRPDrill ? (r.rmaAcres/1000000).toFixed(1)+'M' : isLRPDrill ? fmt(r.rmaAcres,0) : fmt(r.rmaAcres,0)) : <span style={{color:'#ddd'}}>—</span>}</td>}
                              {hasRma && <td style={TD3(true)}>
                                {acShare != null ? (
                                  <div style={{display:'flex',alignItems:'center',gap:6,justifyContent:'flex-end'}}>
                                    <div style={{width:50,height:6,background:'#e8ece8',borderRadius:3,overflow:'hidden'}}>
                                      <div style={{width:`${Math.min(acShare,100)}%`,height:'100%',background:barColor,borderRadius:3}}/>
                                    </div>
                                    <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,fontSize:12,color:barColor}}>{acShare.toFixed(1)}%</span>
                                  </div>
                                ) : <span style={{color:'#ddd',display:'block',textAlign:'right'}}>—</span>}
                              </td>}
                              {hasBu && <td style={{...TD3(true),fontVariantNumeric: "tabular-nums",fontWeight:700,color:'#6b4f00'}}>{r.buGuar ? fmt(r.buGuar,0)+' bu' : <span style={{color:'#ddd'}}>—</span>}</td>}
                              <td style={{...TD3(true),fontVariantNumeric: "tabular-nums",fontWeight:800,color:'#1a2e1a'}}>{fmtD0(isPRFDrill ? r.net : r.gross)}</td>
                            </tr>
                            {isExp && r.farmers.map((f,fi) => (
                              <tr key={`f${fi}`} style={{background:'#f7fbf7',borderBottom:fi===r.farmers.length-1?'2px solid #d0e8d0':'1px solid #eaf3ea'}}>
                                <td style={{padding:'7px 12px 7px 32px',color:'#2d4a2d',fontWeight:600,fontSize:12}}>{f.name}</td>
                                <td/>
                                <td style={{padding:'7px 12px',textAlign:'right',fontVariantNumeric: "tabular-nums",fontSize:12,color:'#2d4a2d'}}>{isDRPDrill ? (f.lbs>0?(f.lbs/1000000).toFixed(1)+'M':<span style={{color:'#ccc'}}>—</span>) : isLRPDrill ? (f.head>0?fmt(f.head,0):<span style={{color:'#ccc'}}>—</span>) : f.acres>0?fmt(f.acres,1):<span style={{color:'#ccc'}}>—</span>}</td>
                                {hasRma && <td/>}{hasRma && <td/>}
                                {hasBu && <td/>}
                                <td style={{padding:'7px 12px',textAlign:'right',fontVariantNumeric: "tabular-nums",fontSize:12,fontWeight:700,color:'#1a2e1a'}}>{f.gross>0?fmtD0(f.gross):<span style={{color:'#ccc'}}>—</span>}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{background:'#f0f7e8',fontWeight:800}}>
                        <td colSpan={2} style={TD3(false)}><span style={{color:'#1a2e1a'}}>Total</span></td>
                        <td style={{...TD3(true),fontVariantNumeric: "tabular-nums",color:'#1a2e1a'}}>{isDRPDrill ? (drillRows.reduce((s,r)=>s+r.acres,0)/1000000).toFixed(1)+'M' : isLRPDrill ? fmt(drillRows.reduce((s,r)=>s+r.acres,0),0) : fmt(drillRows.reduce((s,r)=>s+r.acres,0),1)}</td>
                        {drillRows.some(r=>r.rmaAcres) && (() => {
                          const totalRma = drillRows.filter(r=>r.rmaAcres).reduce((s,r)=>s+r.rmaAcres,0);
                          const totalOur = drillRows.filter(r=>r.rmaAcres).reduce((s,r)=>s+r.acres,0);
                          const share = totalRma>0?(totalOur/totalRma*100):null;
                          return (<><td style={{...TD3(true),fontVariantNumeric: "tabular-nums",color:'#9a8060',fontSize:11}}>{isDRPDrill ? (totalRma/1000000).toFixed(1)+'M' : fmt(totalRma,0)}</td><td style={{...TD3(true),fontVariantNumeric: "tabular-nums",fontWeight:800,color:'#2d4a2d'}}>{share!=null?`${share.toFixed(1)}%`:''}</td></>);
                        })()}
                        {drillRows.some(r=>r.buGuar) && <td style={{...TD3(true),fontVariantNumeric: "tabular-nums",color:'#6b4f00'}}>{fmt(drillRows.reduce((s,r)=>s+(r.buGuar||0),0),0)} bu</td>}
                        <td style={{...TD3(true),fontVariantNumeric: "tabular-nums",color:'#1a2e1a'}}>{fmtD0(drillRows.reduce((s,r)=>s+(isPRFDrill?r.net:r.gross),0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}
      {/* ── FORWARD YEAR — what the CROP side has and has not rolled ─────────
          Sits UNDER the same banner + stat boxes every other year renders, not
          instead of them (Kevin, 2026-08-18: "keep the format the same"). The
          livestock rows moved OUT of this panel because they are now cards above
          it, and one number printed twice on one screen is a number that can
          disagree with itself. Every line here is COUNTED, never asserted: it
          reads whatever summarizeForwardYear measured off the raw coverage rows
          at load, so the moment acreage reporting starts these numbers move on
          their own rather than waiting for someone to delete a stale sentence. */}
      {forwardYearView && (() => {
        const y = parseInt(yearFilter);
        const fy = (FORWARD_YEAR && FORWARD_YEAR.year === y) ? FORWARD_YEAR : null;
        const prfCount = farmers.reduce((s,f) => s + (f.prfPolicies||[]).filter(p => Number(p.cropYear) === y).length, 0);
        const box = { background:"#fff", border:"1px solid #e4dfd6", borderRadius:10, padding:"14px 18px" };
        const rows = [
          // A rolled APH database is not coverage, and `keyed` is what proves it
          // rather than a sentence claiming it. Once acreage reporting starts, the
          // count moves and the qualifier drops itself.
          { lob:"Fall crops (wheat, barley)", sold: !!(fy && fy.keyed),
            val: fy && fy.fall ? `${fmt(fy.fall,0)} APH database${fy.fall===1?'':'s'} rolled` : "nothing on file",
            note: !fy || !fy.fall ? `no CY${y} rows in crop_coverage`
              : fy.keyed ? `${fmt(fy.keyed,0)} with acres keyed · ${fmtD0(fy.premium)} premium`
              : "none keyed yet · $0 premium — a rolled database is not coverage" },
          { lob:"Spring crops", sold: false,
            val: fy && fy.spring ? `${fmt(fy.spring,0)} row${fy.spring===1?'':'s'}` : "nothing on file",
            note: "acreage keys after next season's reporting deadline" },
          { lob:"PRF", sold: prfCount > 0,
            val: prfCount ? `${prfCount} polic${prfCount===1?'y':'ies'}` : "nothing on file",
            note: "sold at the autumn sales closing" },
        ];
        return (
          <div style={{ ...box, marginBottom:20 }}>
            <div style={{ fontSize:13.5, fontWeight:800, color:"#1a2e1a", marginBottom:3 }}>
              CY{y} crop side — ahead of the book (CY{CURRENT_CROP_YEAR})
              {fy && fy.farms ? <span style={{ fontWeight:600, color:"#60735f", fontSize:11.5 }}>{" · "}{fy.farms} farm{fy.farms===1?"":"s"} carry CY{y} paper</span> : null}
            </div>
            <div style={{ fontSize:11.5, color:"#60735f", marginBottom:12, lineHeight:1.5, maxWidth:640 }}>
              The lines of business don’t roll together. Livestock rolls July&nbsp;1 and is
              sold, which is why only those two cards above carry a figure. The crop side is
              APH databases the reinsurance-year file created, not coverage — so every crop
              card reads 0&nbsp;ac and $0 until acreage reporting keys them.
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:1, background:"#eef2ee", border:"1px solid #eef2ee", borderRadius:8, overflow:"hidden" }}>
              {rows.map(r => (
                <div key={r.lob} style={{ display:"flex", flexWrap:"wrap", gap:10, alignItems:"baseline", background:"#fff", padding:"9px 13px" }}>
                  <span style={{ fontSize:12.5, fontWeight:700, color: r.sold ? "#1a2e1a" : "#60735f", minWidth:210 }}>{r.lob}</span>
                  <span style={{ fontVariantNumeric: "tabular-nums", fontSize:12.5, fontWeight:800, color: r.sold ? "#2a5a1a" : "#a0a89a" }}>{r.val}</span>
                  <span style={{ fontSize:11, color:"#677167", marginLeft:"auto", textAlign:"right" }}>{r.note}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}


      {/* ── DRP ── */}
      {/* ── AIP BREAKDOWN ── */}
      {(() => {
        // Year filter logic:
        // 2026 (current): show 2026 live only
        // 2025 (historical): show 2025 historical only
        // 2027 (forward): live too — livestock has rolled, the crop rows are
        //   dropped by the no-acres-no-premium guard, so the table narrows to
        //   what was actually sold rather than printing a page of zeros.
        // All Years: show both
        const selectedYear = parseInt(yearFilter) || CURRENT_CROP_YEAR;
        const showingCurrentYear = !isHistoricalYear && yearFilter !== 'all';
        const showHistorical = isHistoricalYear || yearFilter === 'all';
        // The live half was labelled with the literal 2026 in five places while
        // the numbers under it followed `dashYear`. That was invisible only
        // because the two always agreed; on a forward year they don't, and it
        // would have gone wrong on its own the day the book rolls.
        const liveYear = selectedYear;

        // Historical data for the relevant year(s)
        const hist2025 = historicalData.filter(h => h.crop_year === 2025);
        // If viewing a specific historical year that isn't 2025, show that year instead
        const histYear = isHistoricalYear ? selectedYear : null;
        const histDisplay = histYear
          ? historicalData.filter(h => h.crop_year === histYear)
          : hist2025;
        const histYearLabel = histYear || 2025;

        // ── 2026: from live farmer objects ──
        // Wheat — use wheatByCounty which already has correct acres/premium after phantom cleanup
        const liveWheat = [];
        wheatFarmers.forEach(f => {
          // The SCO/ECO premium is a per farmer+county figure, so it must be added
          // ONCE. Adding it inside the per-row loop re-counted a county's whole
          // overlay for every extra base row that county had — one Rain & Hail
          // farmer with two base wheat rows in a single county put $38,764 of
          // phantom premium on this table and was the entire reason wheat here
          // disagreed with the Dashboard.
          const countedCounty = new Set();
          (f.approvedYields||[]).filter(a => a.crop === 'WHEAT' && inDashYear(a.cropYear) && a.plan && !a.plan.includes('ECO') && !a.plan.includes('SCO') && Number(a.acres) > 0).forEach(a => {
            const cty = (a.county||'').toUpperCase();
            const scoEcoPrem = countedCounty.has(cty) ? 0
              : (f.scoEcoEndorsements||[]).filter(e => inDashYear(e.cropYear)
                  && (e.crop||'').toUpperCase()==='WHEAT' && (e.county||'').toUpperCase()===cty)
                .reduce((s,e)=>s+(Number(e.totalPremium)||0),0);
            countedCounty.add(cty);
            // Carrier from the policy that wrote the row, not the farmer's merged
            // aip — same reason as PRF below.
            // `acres` is INSURED and INCLUDES prevent plant; ppAcres rides along so
            // the live rows can call it out the way the historical rows do. Taken
            // from the SAME approvedYields row that supplies acres, so the two can
            // never describe different coverage. NULL (not determined) → 0, matching
            // what historical_summaries.pp_acres does one section up.
            liveWheat.push({ aip: a.aip || f.aip || 'NAU', acres: Number(a.acres)||0, pp: Number(a.ppAcres)||0, gross: (Number(a.totalPremium)||0) + scoEcoPrem });
          });
        });
        // PRF
        const livePRF = [];
        prfFarmers.forEach(f => {
          (f.prfPolicies||[]).forEach(p => {
            // Carrier from the PRF POLICY, not the farmer — a farm holding both
            // NAU and R&H paper merges to aip:"Multiple" and its PRF landed in a
            // bucket of its own. Mirrors what LRP already does below.
            const polAip = p.aip || (p.policyNumber?.startsWith('MP-') ? 'RAIN_AND_HAIL' : (f.aip||'NAU'));
            // PRF insures rainfall on grid acres — there is no prevent plant on it.
            livePRF.push({ aip: polAip, acres: Number(p.totalAcres)||0, pp: 0, gross: Number(p.totalPremium)||0 });
          });
        });
        // LRP — derive AIP from the policy, not farmer-level aip. lrpPolicies
        // carries the real one; the MP- prefix is only a fallback for endorsements
        // that arrived without it.
        const liveLRP = [];
        lrpFarmers.forEach(f => {
          (f.lrpPolicies||[]).forEach(p => {
            const polAip = p.aip || (p.policyNumber?.startsWith('MP-') ? 'RAIN_AND_HAIL' : (f.aip||'NAU'));
            (p.endorsements||[]).forEach(e => {
              liveLRP.push({ aip: polAip, acres: 0, gross: Number(e.premium)||0 });
            });
          });
        });
        // DRP — use aip stored on each policy object
        // Filter by cropYear: only CY2026 endorsements for live view
        // Dedup by farmer name + drpEndKey — when duplicate exists across CY policies,
        // keep the copy with the earliest cropYear (that's when it was originally written).
        const liveDRP = [];
        const liveDRP2025 = [];
        const _drpEndMap = {};
        drpFarmers.forEach(f => {
          const nameKey = (f.name||'').trim().toLowerCase();
          if (!_drpEndMap[nameKey]) _drpEndMap[nameKey] = {};
          (f.drpPolicies||[]).forEach(p => {
            const polAip = p.aip || (p.policyNumber?.startsWith('MP-') ? 'RAIN_AND_HAIL' : 'NAU');
            (p.endorsements||[]).forEach(e => {
              const dedupKey = drpEndKey(e);
              const existing = _drpEndMap[nameKey][dedupKey];
              if (!existing || (e.cropYear && (!existing.cropYear || e.cropYear < existing.cropYear))) {
                _drpEndMap[nameKey][dedupKey] = { ...e, _aip: polAip };
              }
            });
          });
        });
        // Now split deduped endorsements by crop year. The live side is the year
        // being DISPLAYED, not the book year: those agree on every year except a
        // forward one, where the book-year test put CY2026's $1.96M of DRP under a
        // "2027 — Live" heading.
        //
        // The split keys on whether the live section RENDERS at all, not on
        // `cy < liveYear`. On the 2025 historical view liveYear is 2025, so a
        // less-than test would route CY2025 into the live bucket that view never
        // draws and drop it out of the historical injection it exists to feed —
        // trading one wrong year for another. Everything that isn't live goes to
        // the historical bucket, which is filtered by histYearLabel where it is
        // consumed, so widening it here cannot leak.
        const liveBucketYear = showingCurrentYear ? liveYear : null;
        Object.values(_drpEndMap).forEach(endMap => {
          Object.values(endMap).forEach(e => {
            const cy = e.cropYear || null;
            if (liveBucketYear && cy === liveBucketYear) {
              liveDRP.push({ aip: e._aip, acres: 0, gross: Number(e.totalPremium)||0, milkLbs: Number(e.declaredMilkProd)||0 });
            } else if (cy) {
              liveDRP2025.push({ cropYear: cy, aip: e._aip, acres: 0, gross: Number(e.totalPremium)||0, milkLbs: Number(e.declaredMilkProd)||0 });
            }
          });
        });

        // Normalize AIP label
        const aipLabel = a => {
          if (!a || a === 'NAU') return 'NAU';
          if (a === 'RAIN_AND_HAIL') return 'Rain & Hail';
          return a;
        };

        // Summarize rows by AIP
        const sumByAip = (rows) => {
          const m = {};
          rows.forEach(r => {
            const k = aipLabel(r.aip);
            if (!m[k]) m[k] = { acres: 0, gross: 0, milkLbs: 0, head: 0, pp: 0 };
            m[k].acres += r.acres;
            m[k].pp += r.pp||0;   // prevent plant, INSIDE acres — never subtracted here
            m[k].gross += r.gross;
            m[k].milkLbs += r.milkLbs||0;
            m[k].head += r.head||0;
          });
          return m;
        };

        // 2025 historical by crop+aip
        const hist2025ByCropAip = {};
        histDisplay.forEach(h => {
          const rawCrop = (h.crop||'').toUpperCase().trim();
          const crop = (rawCrop === 'PRF' || rawCrop.startsWith('PASTURE')) ? 'PRF'
            : rawCrop === 'FEEDER CATTLE' || rawCrop === 'FED CATTLE' ? 'LRP'
            : rawCrop === 'MILK' ? 'DRP'
            : rawCrop;
          const plan = h.type||'';
          const isScoEco = plan === 'SCO-RP' || plan === 'ECO-RP';
          const aip = aipLabel(h.aip||'NAU');
          const key = `${crop}||${aip}`;
          if (!hist2025ByCropAip[key]) hist2025ByCropAip[key] = { acres: 0, gross: 0, milkLbs: 0, head: 0, pp: 0 };
          // `acres` on historical_summaries is the INSURED total and INCLUDES
          // prevent plant — the AIP charges premium on PP acres, so that's the
          // basis the gross-premium column sits on. But it is not the number you
          // compare to an SOI's "Insured Acres" column, and 2025 was a heavy PP
          // year (14% of the corn line), so show planted with PP called out.
          // pp_acres NULL means "not determined" (PRF/LRP rows, and Rohrbaugh's
          // Agri Sompo policy, whose statement we don't have) — those contribute
          // no PP rather than a guessed one.
          if (!isScoEco) {
            hist2025ByCropAip[key].acres += h.acres||0;
            hist2025ByCropAip[key].pp += h.pp_acres||0;
          }
          hist2025ByCropAip[key].gross += h.gross_premium||0;
          // LRP historical records store head count in acres field (no separate column)
          if (crop === 'LRP') hist2025ByCropAip[key].head += h.acres||0;
        });

        // Inject prior-crop-year DRP endorsements from live farmer data into historical bucket.
        // DRP spans crop years (e.g. CY2025 policy covers Q1 2026), so live drpPolicies hold
        // endorsements from prior crop years that historical_summaries may not include.
        // Note: if historical_summaries ALSO has DRP/MILK rows for this year, totals will stack.
        // This is acceptable because historical imports typically exclude DRP (handled live).
        liveDRP2025.forEach(r => {
          if (r.cropYear !== histYearLabel) return; // only inject into the year being displayed
          const aip = aipLabel(r.aip);
          const key = `DRP||${aip}`;
          if (!hist2025ByCropAip[key]) hist2025ByCropAip[key] = { acres: 0, gross: 0, milkLbs: 0, head: 0 };
          hist2025ByCropAip[key].gross += r.gross;
          hist2025ByCropAip[key].milkLbs += r.milkLbs||0;
        });

        // Build 2026 live by crop
        // Every non-WHEAT crop in the book — corn, soybeans, dry beans, oats, barley,
        // triticale, potatoes, cabbage, sweet corn, etc. Derived dynamically from the live
        // farmer data (not a hardcoded list) and summed directly from approvedYields, the
        // same source/method as the WHEAT row above. This naturally surfaces fall crops
        // (triticale/barley) too, which the dashboard's spring-only KPI logic would zero out.
        // Crops with no acres AND no premium are hidden by the empty-row guard in the render.
        const SPECIAL_LIVE_CROPS = new Set(['WHEAT','WINTER WHEAT','PRF','LRP','DRP']);
        const liveOtherByCrop = {};  // crop -> [{aip, acres, pp, gross}]
        // `pp` is positional and every call site states it, including the ones that
        // are structurally zero (SCO/ECO carries premium and no acres). An omitted
        // trailing argument is how a PP figure goes quietly missing on one crop.
        const pushOther = (crop, aip, acres, pp, gross) => {
          if (!liveOtherByCrop[crop]) liveOtherByCrop[crop] = [];
          liveOtherByCrop[crop].push({ aip, acres, pp, gross });
        };
        af.forEach(f => {
          // Carrier comes from the POLICY that wrote each row, never the farmer's
          // merged `aip` — that one is set from whichever policy the loader saw
          // first and is never upgraded to "Multiple" here, so a farm holding two
          // carriers' paper put ALL of its crop premium in one arbitrary bucket.
          // Wheat, PRF and LRP above already do this; this loop is every other
          // crop. Merrimac Farms (ProAg corn + soybeans, R&H and NAU PRF) is the
          // case that surfaced it. Farmer aip stays as the last-resort fallback.
          const fAip = f.aip||'NAU';
          (f.approvedYields||[]).forEach(a => {
            const crop = (a.crop||'').toUpperCase().trim();
            if (!crop || SPECIAL_LIVE_CROPS.has(crop)) return;
            if (!a.plan || a.plan.includes('ECO') || a.plan.includes('SCO')) return;
            pushOther(crop, a.aip || fAip, Number(a.acres)||0, Number(a.ppAcres)||0, Number(a.totalPremium)||0);
          });
          // SCO/ECO endorsements carry premium but no acres (they duplicate base-plan acres)
          // — and therefore no PP of their own; the base row above already carries it.
          (f.scoEcoEndorsements||[]).forEach(e => {
            const crop = (e.crop||'').toUpperCase().trim();
            if (!crop || SPECIAL_LIVE_CROPS.has(crop)) return;
            pushOther(crop, e.aip || fAip, 0, 0, Number(e.totalPremium)||0);
          });
        });

        const live2026 = {
          'WHEAT': sumByAip(liveWheat),
          'PRF':   sumByAip(livePRF),
          'LRP':   sumByAip(liveLRP),
          'DRP':   sumByAip(liveDRP),
        };
        Object.keys(liveOtherByCrop).forEach(crop => { live2026[crop] = sumByAip(liveOtherByCrop[crop]); });

        // Crop order for the 2026 Live section: WHEAT, then the other crops (preferred order,
        // unknowns alphabetical), then the non-acre coverage types PRF/LRP/DRP.
        const OTHER_CROP_ORDER = ['CORN','SOYBEANS','DRY BEANS','PROCESSING BEANS','GRAIN SORGHUM','OATS','BARLEY','TRITICALE','RYE','POTATOES','CABBAGE','ONIONS','SWEET CORN'];
        const otherLiveCrops = Object.keys(liveOtherByCrop).sort((a,b) => {
          const ai = OTHER_CROP_ORDER.indexOf(a), bi = OTHER_CROP_ORDER.indexOf(b);
          return (ai===-1?99:ai) - (bi===-1?99:bi) || a.localeCompare(b);
        });
        const LIVE_2026_CROPS = ['WHEAT', ...otherLiveCrops, 'PRF','LRP','DRP'];

        // All AIPs present across both years — use shared sort order
        const allAips = [...new Set([
          ...Object.keys(hist2025ByCropAip).map(k => k.split('||')[1]),
          ...Object.values(live2026).flatMap(m => Object.keys(m)),
        ])].sort((a,b) => {
          const ai = AIP_SORT_ORDER.indexOf(a); const bi = AIP_SORT_ORDER.indexOf(b);
          return (ai===-1?99:ai) - (bi===-1?99:bi);
        });

        // Crop rows: 2025 all crops (from historical_summaries + live DRP injections), 2026 Wheat/PRF/LRP/DRP
        const crops2025 = [...new Set([
          ...histDisplay.map(h => {
            const r = (h.crop||'').toUpperCase().trim();
            return (r === 'PRF' || r.startsWith('PASTURE')) ? 'PRF'
              : r === 'FEEDER CATTLE' || r === 'FED CATTLE' ? 'LRP'
              : r === 'MILK' ? 'DRP' : r;
          }),
          // Include crops from injected live data (e.g. DRP CY2025 endorsements)
          ...Object.keys(hist2025ByCropAip).map(k => k.split('||')[0]),
        ])];
        const CROP_ORDER_HIST = ['WHEAT','CORN','SOYBEANS','DRY BEANS','PROCESSING BEANS','POTATOES','PRF','LRP','DRP'];
        const sortCrop = c => { const i = CROP_ORDER_HIST.indexOf(c); return i === -1 ? 99 : i; };

        const cropLabel = c => c === 'PASTURE,RANGELAND,FORAGE' ? 'PRF'
          : c === 'DRY BEANS' ? 'Dry Beans'
          : c === 'PROCESSING BEANS' ? 'Proc. Beans'
          : c.charAt(0) + c.slice(1).toLowerCase();

        const TH = (right) => ({ padding:'7px 12px', textAlign:right?'right':'left', fontSize:10, fontWeight:800, letterSpacing:'0.08em', color:'#7a6a4a', textTransform:'uppercase', borderBottom:'2px solid #e8d8b0', whiteSpace:'nowrap', background:'#faf4e8' });
        const TD = (right) => ({ padding:'7px 12px', textAlign:right?'right':'left', borderBottom:'1px solid #f0e8d0', verticalAlign:'top' });

        // `acres` is the INSURED total and INCLUDES prevent plant. That is the
        // headline on purpose: the grower paid premium on the PP acres, so this
        // is the basis the gross-premium column beside it sits on, and subtracting
        // PP would make $/ac read high against a number nobody was billed.
        // `pp` is shown underneath, not subtracted — it's what reconciles this
        // figure to an SOI, whose "Insured Acres" column excludes PP.
        const CellVal = ({ acres, gross, pct, bookPct, drilled, onClick, milkLbs, head, pp }) => (
          <div onClick={onClick} style={{cursor: onClick ? 'pointer' : 'default'}}>
            {acres > 0 && <div style={{fontVariantNumeric: "tabular-nums",fontWeight:700,fontSize:12,color:'#1a2e1a'}}>{fmt(acres,0)} ac</div>}
            {pp > 0 && <div style={{fontVariantNumeric: "tabular-nums",fontSize:10.5,color:'#b08030'}} title={`Premium was paid on all ${fmt(acres,0)} ac. ${fmt(pp,0)} of them were prevent plant, so an SOI's "Insured Acres" column reads ${fmt(acres-pp,0)}.`}>incl. {fmt(pp,0)} PP</div>}
            {milkLbs > 0 && <div style={{fontVariantNumeric: "tabular-nums",fontWeight:700,fontSize:12,color:'#1a2e1a'}}>{(milkLbs/1000000).toFixed(1)}M lbs</div>}
            {head > 0 && <div style={{fontVariantNumeric: "tabular-nums",fontWeight:700,fontSize:12,color:'#1a2e1a'}}>{fmt(head,0)} hd</div>}
            {gross > 0 && <div style={{fontVariantNumeric: "tabular-nums",fontSize:11,color:'#6a8a5a'}}>{fmtD0(gross)}</div>}
            {pct != null && gross > 0 && <div style={{fontSize:10,color:'#7a9a7a',marginTop:1}}>{pct.toFixed(1)}% of crop</div>}
            {bookPct != null && gross > 0 && <div style={{fontSize:10,color:'#a09070',marginTop:1}}>{bookPct.toFixed(1)}% of book</div>}
            {acres === 0 && gross === 0 && !milkLbs && !head && <span style={{color:'#ddd',fontSize:11}}>—</span>}
            {onClick && gross > 0 && <div style={{fontSize:10,color:'#4a7a4a',marginTop:1}}>{drilled ? '▲ hide' : '▼ farmers'}</div>}
          </div>
        );

        // Farmer-level lookups for drill-down
        // Historical: crop||aip -> {farmerName: {acres, gross}}
        const histFarmersByCropAip = {};
        histDisplay.forEach(h => {
          const rawCrop = (h.crop||'').toUpperCase().trim();
          const crop = (rawCrop === 'PRF' || rawCrop.startsWith('PASTURE')) ? 'PRF'
            : rawCrop === 'FEEDER CATTLE' || rawCrop === 'FED CATTLE' ? 'LRP'
            : rawCrop === 'MILK' ? 'DRP' : rawCrop;
          const plan = h.type||'';
          const isScoEco = plan === 'SCO-RP' || plan === 'ECO-RP';
          const aip = aipLabel(h.aip||'NAU');
          const key = `${crop}||${aip}`;
          if (!histFarmersByCropAip[key]) histFarmersByCropAip[key] = {};
          const name = h.farmer_name || '(Unknown)';
          if (!histFarmersByCropAip[key][name]) histFarmersByCropAip[key][name] = { acres: 0, gross: 0, pp: 0 };
          if (!isScoEco) {
            histFarmersByCropAip[key][name].acres += h.acres||0;
            histFarmersByCropAip[key][name].pp += h.pp_acres||0;   // see note on hist2025ByCropAip
          }
          histFarmersByCropAip[key][name].gross += h.gross_premium||0;
        });

        // Inject prior-crop-year DRP farmer drill data from live farmer objects
        // Dedup: earliest cropYear wins for each drpEndKey (never endDate+endNum)
        const _histDrpMap = {};
        allDrpFarmers.forEach(f => {
          const nameKey = (f.name||'').trim().toLowerCase();
          if (!_histDrpMap[nameKey]) _histDrpMap[nameKey] = { name: f.name, endMap: {} };
          (f.drpPolicies||[]).forEach(p => {
            const polAip = p.aip || (p.policyNumber?.startsWith('MP-') ? 'RAIN_AND_HAIL' : 'NAU');
            (p.endorsements||[]).forEach(e => {
              const dk = drpEndKey(e);
              const existing = _histDrpMap[nameKey].endMap[dk];
              if (!existing || (e.cropYear && (!existing.cropYear || e.cropYear < existing.cropYear))) {
                _histDrpMap[nameKey].endMap[dk] = { ...e, _aip: polAip };
              }
            });
          });
        });
        Object.values(_histDrpMap).forEach(({ name, endMap }) => {
          Object.values(endMap).forEach(e => {
            if (e.cropYear && e.cropYear === histYearLabel) {
              const aip = aipLabel(e._aip);
              const key = `DRP||${aip}`;
              if (!histFarmersByCropAip[key]) histFarmersByCropAip[key] = {};
              if (!histFarmersByCropAip[key][name]) histFarmersByCropAip[key][name] = { acres: 0, gross: 0 };
              histFarmersByCropAip[key][name].gross += Number(e.totalPremium)||0;
            }
          });
        });
        const liveFarmersByCropAip = {};
        // `pp` is positional here too, and DrillTable has always rendered it — the
        // 2026 rows just never supplied one, so the drill-down under a live cell
        // showed insured acres with no note that some of them were prevent plant
        // while the 2025 drill-down right above it did.
        const addLF = (crop, aip, name, acres, pp, gross) => {
          const key = `${crop}||${aip}`;
          if (!liveFarmersByCropAip[key]) liveFarmersByCropAip[key] = {};
          if (!liveFarmersByCropAip[key][name]) liveFarmersByCropAip[key][name] = { acres: 0, pp: 0, gross: 0 };
          liveFarmersByCropAip[key][name].acres += acres;
          liveFarmersByCropAip[key][name].pp += pp;
          liveFarmersByCropAip[key][name].gross += gross;
        };
        wheatFarmers.forEach(f => {
          (f.approvedYields||[]).filter(a => a.crop==='WHEAT' && a.plan && !a.plan.includes('ECO') && !a.plan.includes('SCO') && Number(a.acres)>0).forEach(a => {
            const scoEcoPrem = (f.scoEcoEndorsements||[]).filter(e=>(e.crop||'').toUpperCase()==='WHEAT'&&(e.county||'').toUpperCase()===(a.county||'').toUpperCase()).reduce((s,e)=>s+(Number(e.totalPremium)||0),0);
            addLF('WHEAT', aipLabel(a.aip || f.aip || 'NAU'), f.name, Number(a.acres)||0, Number(a.ppAcres)||0, (Number(a.totalPremium)||0) + scoEcoPrem);
          });
        });
        prfFarmers.forEach(f => {
          (f.prfPolicies||[]).forEach(p => {
            addLF('PRF', aipLabel(p.aip || (p.policyNumber?.startsWith('MP-') ? 'RAIN_AND_HAIL' : (f.aip||'NAU'))), f.name, Number(p.totalAcres)||0, 0, Number(p.totalPremium)||0);
          });
        });
        lrpFarmers.forEach(f => {
          (f.lrpPolicies||[]).forEach(pol => {
            const polAip = pol.aip || (pol.policyNumber?.startsWith('MP-') ? 'RAIN_AND_HAIL' : (f.aip||'NAU'));
            const gross = (pol.endorsements||[]).reduce((s,e)=>s+(Number(e.premium)||0),0);
            if (gross>0) addLF('LRP', aipLabel(polAip), f.name, 0, 0, gross);
          });
        });
        // DRP drill-down: dedup by drpEndKey, earliest cropYear wins
        const _drpDrillMap = {};
        drpFarmers.forEach(f => {
          const nameKey = (f.name||'').trim().toLowerCase();
          if (!_drpDrillMap[nameKey]) _drpDrillMap[nameKey] = { name: f.name, endMap: {} };
          (f.drpPolicies||[]).forEach(pol => {
            const polAip = pol.aip || (pol.policyNumber?.startsWith('MP-') ? 'RAIN_AND_HAIL' : 'NAU');
            (pol.endorsements||[]).forEach(e => {
              const dk = drpEndKey(e);
              const existing = _drpDrillMap[nameKey].endMap[dk];
              if (!existing || (e.cropYear && (!existing.cropYear || e.cropYear < existing.cropYear))) {
                _drpDrillMap[nameKey].endMap[dk] = { ...e, _aip: polAip };
              }
            });
          });
        });
        // Displayed year, same as the summary bucket above — a drill-down that
        // opens from a cell must be the same endorsements the cell counted.
        Object.values(_drpDrillMap).forEach(({ name, endMap }) => {
          const liveEnds = Object.values(endMap).filter(e => e.cropYear === liveYear);
          const gross = liveEnds.reduce((s,e)=>s+(Number(e.totalPremium)||0),0);
          if (gross > 0) addLF('DRP', aipLabel(liveEnds[0]?._aip||'NAU'), name, 0, 0, gross);
        });
        // Other-crop drill-down (every non-WHEAT/PRF/LRP/DRP crop) — direct from approvedYields
        af.forEach(f => {
          // Per-row carrier, same as the summary above — otherwise a multi-carrier
          // farm's drill-down disagrees with the bucket it opens from.
          const fAip = f.aip||'NAU';
          (f.approvedYields||[]).forEach(a => {
            const crop = (a.crop||'').toUpperCase().trim();
            if (!crop || SPECIAL_LIVE_CROPS.has(crop)) return;
            if (!a.plan || a.plan.includes('ECO') || a.plan.includes('SCO')) return;
            const acres = Number(a.acres)||0, gross = Number(a.totalPremium)||0;
            if (acres > 0 || gross > 0) addLF(crop, aipLabel(a.aip || fAip), f.name, acres, Number(a.ppAcres)||0, gross);
          });
          (f.scoEcoEndorsements||[]).forEach(e => {
            const crop = (e.crop||'').toUpperCase().trim();
            if (!crop || SPECIAL_LIVE_CROPS.has(crop)) return;
            const gross = Number(e.totalPremium)||0;
            if (gross > 0) addLF(crop, aipLabel(e.aip || fAip), f.name, 0, 0, gross);
          });
        });

        // Shared drill sub-table component
        const DrillTable = ({ farmers, color }) => (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background: color+'22'}}>
              <th style={{padding:'4px 24px',textAlign:'left',fontSize:10,fontWeight:800,letterSpacing:'0.08em',color:color,textTransform:'uppercase'}}>Farmer</th>
              <th style={{padding:'4px 12px',textAlign:'right',fontSize:10,fontWeight:800,letterSpacing:'0.08em',color:color,textTransform:'uppercase'}}>Acres</th>
              <th style={{padding:'4px 12px',textAlign:'right',fontSize:10,fontWeight:800,letterSpacing:'0.08em',color:color,textTransform:'uppercase'}}>Gross Prem</th>
            </tr></thead>
            <tbody>
              {Object.entries(farmers).sort((a,b)=>b[1].gross-a[1].gross).map(([name,v],i) => (
                <tr key={i} style={{borderBottom:'1px solid '+color+'22'}}>
                  <td style={{padding:'5px 12px 5px 24px',fontWeight:600,color:'#1a2e1a'}}>{name}</td>
                  <td style={{padding:'5px 12px',textAlign:'right',fontVariantNumeric: "tabular-nums",color:'#666'}}>
                    {v.acres > 0 ? fmt(v.acres,0)+' ac' : '—'}
                    {v.pp > 0 && <div style={{fontSize:10.5,color:'#b08030'}} title={`Premium was paid on all ${fmt(v.acres,0)} ac. ${fmt(v.pp,0)} of them were prevent plant, so an SOI's "Insured Acres" column reads ${fmt(v.acres-v.pp,0)}.`}>incl. {fmt(v.pp,0)} PP</div>}
                  </td>
                  <td style={{padding:'5px 12px',textAlign:'right',fontVariantNumeric: "tabular-nums",fontWeight:700,color:'#1a2e1a'}}>{fmtD0(v.gross)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );

        // Section totals per AIP
        const totals2025 = {};
        const totals2026 = {};
        allAips.forEach(aip => {
          totals2025[aip] = { acres: 0, gross: 0, pp: 0 };
          totals2026[aip] = { acres: 0, gross: 0, pp: 0 };
        });
        crops2025.forEach(crop => {
          allAips.forEach(aip => {
            const k = `${crop}||${aip}`;
            const v = hist2025ByCropAip[k]||{acres:0,gross:0,pp:0};
            totals2025[aip].acres += v.acres;
            totals2025[aip].pp    += v.pp||0;
            totals2025[aip].gross += v.gross;
          });
        });
        LIVE_2026_CROPS.forEach(crop => {
          allAips.forEach(aip => {
            const v = live2026[crop]?.[aip]||{acres:0,gross:0,pp:0};
            totals2026[aip].acres += v.acres;
            totals2026[aip].pp    += v.pp||0;
            totals2026[aip].gross += v.gross;
          });
        });

        // Grand total gross across all years shown — for % of book calculation
        const grandTotalGross =
          (showHistorical ? allAips.reduce((s,a)=>s+totals2025[a].gross,0) : 0) +
          (showingCurrentYear ? allAips.reduce((s,a)=>s+totals2026[a].gross,0) : 0);

        const colCount = allAips.length + 2; // crop col + aip cols + total col

        return (
          <CollapsibleSection title="📊 Book of Business — By AIP" accent="default"
            subtitle={(() => {
              const labels = allAips.map(aipLabel).join(' · ');
              return yearFilter === 'all' ? `${labels} · ${histYearLabel} historical + ${liveYear} live` : showingCurrentYear ? `${labels} · ${liveYear} live` : `${labels} · ${histYearLabel} historical`;
            })()}>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr>
                    <th style={TH(false)}>Crop / Coverage</th>
                    {allAips.map(aip => <th key={aip} style={TH(true)}>{aipLabel(aip)}</th>)}
                    <th style={TH(true)}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {/* ── Historical — only shown on historical year or All Years ── */}
                  {showHistorical && <>
                  <tr>
                    <td colSpan={allAips.length+2} style={{padding:'8px 12px',background:'#faf4e8',fontSize:10,fontWeight:800,letterSpacing:'0.1em',color:'#8a6a2a',textTransform:'uppercase'}}>
                      {histYearLabel} — Historical
                    </td>
                  </tr>
                  {crops2025.sort((a,b) => sortCrop(a)-sortCrop(b)).map(crop => {
                    const totalAcres = allAips.reduce((s,aip) => s+(hist2025ByCropAip[`${crop}||${aip}`]?.acres||0),0);
                    const totalGross = allAips.reduce((s,aip) => s+(hist2025ByCropAip[`${crop}||${aip}`]?.gross||0),0);
                    const totalMilkLbs = allAips.reduce((s,aip) => s+(hist2025ByCropAip[`${crop}||${aip}`]?.milkLbs||0),0);
                    const totalHead = allAips.reduce((s,aip) => s+(hist2025ByCropAip[`${crop}||${aip}`]?.head||0),0);
                    const totalPP = allAips.reduce((s,aip) => s+(hist2025ByCropAip[`${crop}||${aip}`]?.pp||0),0);
                    if (totalAcres === 0 && totalGross === 0) return null;
                    return (
                      <React.Fragment key={`2025-${crop}`}>
                        <tr style={{background:'#fff'}}>
                          <td style={TD(false)}><span style={{fontWeight:700,color:'#3a2a0a'}}>{cropLabel(crop)}</span></td>
                          {allAips.map(aip => {
                            const v = hist2025ByCropAip[`${crop}||${aip}`]||{acres:0,gross:0,milkLbs:0,head:0,pp:0};
                            const pct = totalGross > 0 ? v.gross / totalGross * 100 : null;
                            const drilled = isDrilled(histYearLabel, crop, aip);
                            return (
                              <React.Fragment key={aip}>
                                <td style={{...TD(true), background: drilled ? '#fdf0e0' : undefined}}>
                                  <CellVal acres={v.acres} gross={v.gross} pct={pct} pp={v.pp}
                                    milkLbs={crop==='DRP' ? v.milkLbs : 0}
                                    head={crop==='LRP' ? v.head : 0}
                                    drilled={drilled}
                                    onClick={v.gross > 0 ? () => toggleDrill(histYearLabel, crop, aip) : null}/>
                                </td>
                              </React.Fragment>
                            );
                          })}
                          <td style={{...TD(true),background:'#fdf8f0'}}>
                            <CellVal acres={totalAcres} gross={totalGross} pp={totalPP}
                              milkLbs={crop==='DRP' ? totalMilkLbs : 0}
                              head={crop==='LRP' ? totalHead : 0}/>
                          </td>
                        </tr>
                        {allAips.map(aip => {
                          if (!isDrilled(histYearLabel, crop, aip)) return null;
                          const farmers = histFarmersByCropAip[`${crop}||${aip}`]||{};
                          return (
                            <tr key={`drill-${aip}`}>
                              <td colSpan={colCount} style={{padding:0}}>
                                <DrillTable farmers={farmers} color="#8a6030"/>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  <tr style={{background:'#f5ead8',fontWeight:800}}>
                    <td style={{...TD(false),fontWeight:800,color:'#6a4a10'}}>{histYearLabel} Total</td>
                    {(() => {
                      const grandHist = allAips.reduce((s,a)=>s+totals2025[a].gross,0);
                      return allAips.map(aip => (
                        <td key={aip} style={TD(true)}>
                          <CellVal acres={totals2025[aip].acres} gross={totals2025[aip].gross} pp={totals2025[aip].pp}
                            bookPct={grandHist > 0 ? totals2025[aip].gross / grandHist * 100 : null}/>
                        </td>
                      ));
                    })()}
                    <td style={TD(true)}>
                      <CellVal
                        acres={allAips.reduce((s,a)=>s+totals2025[a].acres,0)}
                        pp={allAips.reduce((s,a)=>s+totals2025[a].pp,0)}
                        gross={allAips.reduce((s,a)=>s+totals2025[a].gross,0)}/>
                    </td>
                  </tr>
                  </>}

                  {/* ── 2026 Live — only shown when viewing current year ── */}
                  {showingCurrentYear && <>
                  <tr>
                    <td colSpan={allAips.length+2} style={{padding:'8px 12px',background:'#eef6ea',fontSize:10,fontWeight:800,letterSpacing:'0.1em',color:'#3a6a2a',textTransform:'uppercase'}}>
                      {liveYear} — Live
                    </td>
                  </tr>
                  {LIVE_2026_CROPS.map(crop => {
                    const m = live2026[crop]||{};
                    const totalAcres = Object.values(m).reduce((s,v)=>s+v.acres,0);
                    const totalGross = Object.values(m).reduce((s,v)=>s+v.gross,0);
                    const totalMilkLbs = Object.values(m).reduce((s,v)=>s+(v.milkLbs||0),0);
                    const totalPP = Object.values(m).reduce((s,v)=>s+(v.pp||0),0);
                    if (totalAcres === 0 && totalGross === 0) return null;
                    return (
                      <React.Fragment key={`2026-${crop}`}>
                        <tr style={{background:'#fbfefb'}}>
                          <td style={TD(false)}><span style={{fontWeight:700,color:'#1a3a0a'}}>{cropLabel(crop)}</span></td>
                          {allAips.map(aip => {
                            const v = m[aip]||{acres:0,gross:0,pp:0};
                            const pct = totalGross > 0 ? v.gross / totalGross * 100 : null;
                            const drilled = isDrilled(liveYear, crop, aip);
                            return (
                              <React.Fragment key={aip}>
                                <td style={{...TD(true), background: drilled ? '#e8f5e0' : undefined}}>
                                  <CellVal acres={v.acres} gross={v.gross} pct={pct} pp={v.pp}
                                    milkLbs={crop==='DRP' ? v.milkLbs||0 : 0}
                                    drilled={drilled}
                                    onClick={v.gross > 0 ? () => toggleDrill(liveYear, crop, aip) : null}/>
                                </td>
                              </React.Fragment>
                            );
                          })}
                          <td style={{...TD(true),background:'#f4faf0'}}>
                            <CellVal acres={totalAcres} gross={totalGross} pp={totalPP}
                              milkLbs={crop==='DRP' ? totalMilkLbs : 0}/>
                          </td>
                        </tr>
                        {allAips.map(aip => {
                          if (!isDrilled(liveYear, crop, aip)) return null;
                          const farmers = liveFarmersByCropAip[`${crop}||${aip}`]||{};
                          return (
                            <tr key={`drill-${aip}`}>
                              <td colSpan={colCount} style={{padding:0}}>
                                <DrillTable farmers={farmers} color="#2a6020"/>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  <tr style={{background:'#dff0d8',fontWeight:800}}>
                    <td style={{...TD(false),fontWeight:800,color:'#2a5a1a'}}>{liveYear} Total</td>
                    {(() => {
                      const grand2026 = allAips.reduce((s,a)=>s+totals2026[a].gross,0);
                      return allAips.map(aip => (
                        <td key={aip} style={TD(true)}>
                          <CellVal acres={totals2026[aip].acres} gross={totals2026[aip].gross} pp={totals2026[aip].pp}
                            bookPct={grand2026 > 0 ? totals2026[aip].gross / grand2026 * 100 : null}/>
                        </td>
                      ));
                    })()}
                    <td style={TD(true)}>
                      <CellVal
                        acres={allAips.reduce((s,a)=>s+totals2026[a].acres,0)}
                        pp={allAips.reduce((s,a)=>s+totals2026[a].pp,0)}
                        gross={allAips.reduce((s,a)=>s+totals2026[a].gross,0)}/>
                    </td>
                  </tr>
                  </>}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        );
      })()}

      <CollapsibleSection title="🥛 DRP — Dairy Revenue Protection" accent="drp"
        badge={drpDashMerged.length>0?`${drpDashMerged.length} policies`:null}
        subtitle={activeDrpPremium>0?`${fmtD0(activeDrpPremium)} active producer premium`:"no policies on file"}>
        {drpDashMerged.length===0 ? (
          <div style={{ textAlign:"center", color:"#9a9a8a", fontSize:13, padding:"16px 0" }}>No DRP policies for selected year.</div>
        ) : (
          <SortableTable
            accent="#3a60aa"
            columns={[
              { key:"name", label:"Farmer", align:"left" },
              { key:"county", label:"County", align:"left" },
              { key:"ends", label:"Endorsements", align:"right" },
              { key:"liability", label:"Liability", align:"right" },
              { key:"premium", label:"Producer Premium", align:"right" },
            ]}
            rows={drpDashMerged.map(r => ({
              name: r.name, county: r.county||"—", ends: r.ends.length,
              liability: r.ends.reduce((s,e)=>s+(e.liability||0),0),
              premium: r.ends.reduce((s,e)=>s+(e.producerPremium||0),0),
            }))}
            defaultSortKey="name"
            defaultSortDir="asc"
            renderRow={(r,i) => (
              <tr key={i} style={{ borderBottom:"1px solid #f0f4fc" }}>
                <td style={{...TD(false),fontWeight:700,color:"#1a2a4a"}}>{r.name}</td>
                <td style={TD(false)}>{r.county}</td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums"}}>{r.ends}</td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums",color:"#1a4a7a"}}>{r.liability>0?fmtD0(r.liability):"—"}</td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums",fontWeight:700,color:"#1a4a7a"}}>{r.premium>0?fmtD0(r.premium):"—"}</td>
              </tr>
            )}
            footer={totalDrpPremium>0 ? (
              <tr style={{background:"#f0f4fc",fontWeight:800}}>
                <td colSpan={3} style={TD(false)}><span style={{color:"#1a2a4a"}}>Total</span></td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums",color:"#1a4a7a"}}>{fmtD0(totalDrpLiability)}</td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums",color:"#1a4a7a"}}>{fmtD0(totalDrpPremium)}</td>
              </tr>
            ) : null}
          />
        )}
      </CollapsibleSection>

      {/* ── LRP ENDING SOON ── */}
      {lrpEndingSoon.length > 0 && (
        <CollapsibleSection title="⏰ LRP Endorsements Ending Soon" accent="lrp"
          badge={`${lrpEndingSoon.length} within 60 days`} badgeColor="#cc5500"
          subtitle="Glance at coverage prices for endorsements ending soon">
          <SortableTable
            accent="#7a3a10"
            columns={[
              { key:"farmerName",    label:"Farmer",           align:"left"  },
              { key:"commodity",     label:"Commodity",        align:"left"  },
              { key:"endDate",       label:"End Date",         align:"left"  },
              { key:"daysLeft",      label:"Status",           align:"right" },
              { key:"head",          label:"Head",             align:"right" },
              { key:"coveragePrice", label:"Coverage Price",   align:"right" },
              { key:"actualEndValue",label:"Actual End Value", align:"right" },
              { key:"payableIndemnity", label:"Indemnity",     align:"right" },
              { key:"billingDate",   label:"Bill Date",        align:"left"  },
            ]}
            rows={lrpEndingSoon.map((r,i) => ({...r, _sortOrder: i}))}
            defaultSortKey="_sortOrder"
            defaultSortDir="asc"
            renderRow={(e,i) => {
              const dateStr = e.endDate.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
              const daysLabel = e.daysLeft < 0 ? "Ended" : e.daysLeft === 0 ? "Today" : `${e.daysLeft}d`;
              const statusColor = e.daysLeft <= 0 ? "#aaa" : "#7a5a40";
              return (
                <tr key={i} style={{ borderBottom:"1px solid #f4ede4", opacity: e.daysLeft < 0 ? 0.6 : 1 }}>
                  <td style={{...TD(false), fontWeight:700, color:"#3a1f0a"}}>{e.farmerName}</td>
                  <td style={TD(false)}><span style={{fontSize:11,background:"#f4ede4",padding:"2px 7px",borderRadius:4,fontWeight:700,color:"#7a3a10"}}>{e.commodity}</span></td>
                  <td style={{...TD(false), fontVariantNumeric: "tabular-nums", fontSize:12}}>{dateStr}</td>
                  <td style={{...TD(true), fontVariantNumeric: "tabular-nums", color:statusColor, fontWeight:700, fontStyle: e.daysLeft < 0 ? "italic" : "normal"}}>{daysLabel}</td>
                  <td style={{...TD(true), fontVariantNumeric: "tabular-nums"}}>{e.head > 0 ? fmt(e.head,0) : "—"}</td>
                  <td style={{...TD(true), fontVariantNumeric: "tabular-nums", color:"#7a3a10", fontWeight:700}}>
                    {e.coveragePrice > 0 ? `$${e.coveragePrice.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}` : "—"}
                    {e.targetWeight > 0 && <span style={{fontWeight:400,color:"#aaa",fontSize:11}}>/cwt</span>}
                  </td>
                  <td style={{...TD(true)}}>
                    {e.daysLeft < 0 && e.actualEndValue > 0 ? (() => {
                      const claimed = e.payableIndemnity > 0;
                      const belowCov = e.actualEndValue < e.coveragePrice;
                      // Red only if indemnity was actually paid; green if settled above coverage or no indemnity
                      const isRed = claimed || (!e.payableIndemnity && belowCov);
                      return (
                        <div style={{display:"inline-block",background:isRed?"#fdecea":"#eef6ec",border:`1px solid ${isRed?"#e07070":"#4a7a2a"}44`,borderRadius:4,padding:"2px 8px"}}>
                          <span style={{fontVariantNumeric: "tabular-nums",fontWeight:800,color:isRed?"#c0392b":"#2d7a2d",fontSize:12}}>${e.actualEndValue.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                        </div>
                      );
                    })() : <span style={{color:"#ccc",fontSize:11}}>{e.daysLeft < 0 ? "—" : ""}</span>}
                  </td>
                  <td style={{...TD(true)}}>
                    {e.daysLeft < 0 ? (
                      e.payableIndemnity > 0
                        ? <span style={{fontSize:12,fontWeight:800,color:"#2d7a2d"}}>✓ ${e.payableIndemnity.toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0})}</span>
                        : e.actualEndValue > 0
                          ? <span style={{fontSize:11,fontWeight:600,color:"#aaa"}}>No indemnity</span>
                          : <span style={{fontSize:11,color:"#ccc"}}>—</span>
                    ) : null}
                  </td>
                  <td style={{...TD(false), fontSize:11, color:"#9a6a40"}}>{e.billingDate || "—"}</td>
                </tr>
              );
            }}
          />
        </CollapsibleSection>
      )}

      {/* ── LRP ── */}
      <CollapsibleSection title="🐄 LRP — Livestock Risk Protection" accent="lrp"
        badge={lrpFarmers.length>0?`${lrpFarmers.length} policies`:null}
        subtitle={activeLrpPremium>0?`${fmtD0(activeLrpPremium)} active producer premium · ${activeLrpHead} active head`:"no policies on file"}>
        {lrpFarmers.length===0 ? (
          <div style={{ textAlign:"center", color:"#9a9a8a", fontSize:13, padding:"16px 0" }}>No LRP policies for selected year.</div>
        ) : (
          <SortableTable
            accent="#7a4a20"
            columns={[
              { key:"name",      label:"Farmer",           align:"left"  },
              { key:"commodity", label:"Commodity",        align:"left"  },
              { key:"ends",      label:"Endorsements",     align:"right" },
              { key:"head",      label:"Head",             align:"right" },
              { key:"indemnity", label:"Total Indemnity",  align:"right" },
              { key:"premium",   label:"Producer Premium", align:"right" },
            ]}
            rows={lrpFarmers.map(f => {
              const allEnds=(f.lrpPolicies||[]).flatMap(p=>p.endorsements||[]);
              const indemnity = allEnds.reduce((s,e) => s + (e.payableIndemnity||0), 0);
              return { name: f.name, commodity: (f.lrpPolicies||[])[0]?.commodity||"—", ends: allEnds.length, head: allEnds.reduce((s,e)=>s+(e.head||0),0), indemnity, premium: allEnds.reduce((s,e)=>s+(e.producerPremium||0),0) };
            })}
            defaultSortKey="name"
            defaultSortDir="asc"
            renderRow={(r,i) => (
              <tr key={i} style={{ borderBottom:"1px solid #fdf0e8" }}>
                <td style={{...TD(false),fontWeight:700,color:"#3a1f0a"}}>{r.name}</td>
                <td style={TD(false)}>{r.commodity}</td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums"}}>{r.ends}</td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums"}}>{r.head>0?fmt(r.head,0):"—"}</td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums",fontWeight:700,color:r.indemnity>0?"#2d7a2d":"#aaa"}}>
                  {r.indemnity>0 ? <>✓ {fmtD0(r.indemnity)}</> : "—"}
                </td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums",fontWeight:700,color:"#7a4a20"}}>{r.premium>0?fmtD0(r.premium):"—"}</td>
              </tr>
            )}
            footer={totalLrpPremium>0 ? (
              <tr style={{background:"#fdf0e8",fontWeight:800}}>
                <td colSpan={3} style={TD(false)}><span style={{color:"#3a1f0a"}}>Total</span></td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums"}}>{fmt(totalLrpHead,0)} head</td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums",fontWeight:800,color:totalLrpIndemnity>0?"#2d7a2d":"#aaa"}}>
                  {totalLrpIndemnity>0 ? <>✓ {fmtD0(totalLrpIndemnity)}</> : "—"}
                </td>
                <td style={{...TD(true),fontVariantNumeric: "tabular-nums",color:"#7a4a20"}}>{fmtD0(totalLrpPremium)}</td>
              </tr>
            ) : null}
          />
        )}
      </CollapsibleSection>

      {/* ── YIELD HISTORY ── */}
      <CollapsibleSection title={`🌽 ${CURRENT_CROP_YEAR - 1} Actual Yield History`} accent="crop"
        subtitle="Weighted average yields by county · Corn, Soybeans, Wheat">
        <YieldHistorySection farmers={farmers} />
      </CollapsibleSection>

      {/* ── EXPORT FULL ROSTER ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, padding:"16px 20px", background:"#f4f8f4", border:"1px solid #d4e4d4", borderRadius:10, marginTop:4 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:14, color:"#1a2e1a" }}>Full Roster Export</div>
          <div style={{ fontSize:12, color:"#60735f" }}>All policies · all crops · acres · APH · bu guarantee</div>
        </div>
        <button onClick={exportFull} style={{ background:"#1a2e1a", color:"#fff", border:"none", borderRadius:7, padding:"9px 20px", fontSize:12, fontWeight:800, letterSpacing:"0.06em", cursor:"pointer" }}>⬇ Export Full CSV</button>
      </div>

      {/* ── DRILL-DOWN MODAL ── */}
      {drill && typeof drill === 'object' && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onClick={e=>{ if(e.target===e.currentTarget) setDrill(null); }}>
          <div style={{ background:"#fff", borderRadius:12, padding:28, maxWidth:640, width:"100%", maxHeight:"80vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
              <div style={{ fontWeight:800, fontSize:16, color:"#1a2e1a" }}>
                {drill.type==="county-farmers" && `${drill.county} County — ${drill.farmers.length} Farm${drill.farmers.length!==1?"s":""}`}
                {drill.type==="county-detail"  && `${drill.county} County — ${drill.label||"Crop"} Detail`}
              </div>
              <button onClick={()=>setDrill(null)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9a9a8a" }}>✕</button>
            </div>
            {drill.type==="county-farmers" && drill.farmers.map((name,i)=>(
              <div key={i} style={{ padding:"10px 14px", background:i%2===0?"#f8f4ec":"#fff", borderRadius:6, marginBottom:4, fontWeight:700, color:"#3a2800", fontSize:13 }}>🌾 {name}</div>
            ))}
            {drill.type==="county-detail" && (
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead><tr style={{ background:"#f8f4ec" }}>
                  {["Farmer","APH","Cov %","Acres","Bu. Guar."].map((h,i)=><th key={h} style={TH(i===0)}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {drill.farmers.flatMap(f =>
                    (f.approvedYields||[]).filter(a=>a.crop===drill.crop&&(a.county||"").toUpperCase()===(drill.county||"").toUpperCase()&&a.plan&&!a.plan.includes("ECO")).map((a,i)=>{
                      const bg=(a.acres&&a.approvedYield&&a.coverageLevel)?a.approvedYield*a.coverageLevel*Number(a.acres):null;
                      return (
                        <tr key={f.name+i} style={{ borderBottom:"1px solid #f4f0e8" }}>
                          <td style={{...TD(false),fontWeight:700,color:"#1a2e1a"}}>{f.name}</td>
                          <td style={{...TD(true),fontVariantNumeric: "tabular-nums"}}>{a.approvedYield?`${a.approvedYield} bu`:<span style={{color:"#c0392b",fontWeight:800}}>⚠ missing</span>}</td>
                          <td style={{...TD(true),fontVariantNumeric: "tabular-nums"}}>{a.coverageLevel?`${(a.coverageLevel*100).toFixed(0)}%`:"—"}</td>
                          <td style={{...TD(true),fontVariantNumeric: "tabular-nums"}}>{a.acres?fmt(a.acres,2):<span style={{color:"#c0392b",fontWeight:800}}>⚠ missing</span>}</td>
                          <td style={{...TD(true),fontVariantNumeric: "tabular-nums",fontWeight:700,color:"#6b4f00"}}>{bg?fmt(bg,0)+" bu":<span style={{color:"#bbb"}}>—</span>}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// WHEAT SPOT CHECK SCREEN
// ─────────────────────────────────────────────────────────────────────────────
const WHEAT_SHEET = [{"name": "A & J KIRBY FARMS LLC", "county": "Orleans", "coverageLevel": 0.75, "reportedAcres": 146.69, "grossPremium": 7266, "producerPremium": 2906}, {"name": "ANCHOR FARMS INC", "county": "Erie", "coverageLevel": 0.5, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "ANDERSON FARMS DAIRY LLC", "county": "Monroe", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "ANDERSON FARMS DAIRY LLC", "county": "Ontario", "coverageLevel": 0.8, "reportedAcres": 117.1, "grossPremium": 6031, "producerPremium": 2955}, {"name": "ANDERSON FARMS DAIRY LLC", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": 223.5, "grossPremium": 10215, "producerPremium": 2962}, {"name": "ARTHUR B DOUGLAS J BENEDICT DBA ARBOR FARMS", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 27.0, "grossPremium": 1058, "producerPremium": 423}, {"name": "ATWATER FARMS LLC", "county": "Niagara", "coverageLevel": 0.5, "reportedAcres": 524.0, "grossPremium": 9450, "producerPremium": 1890}, {"name": "ATWATER FARMS LLC", "county": "Orleans", "coverageLevel": 0.5, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "AUSTIC FARM PARTNERS", "county": "Tompkins", "coverageLevel": 0.75, "reportedAcres": 28.1, "grossPremium": 1261, "producerPremium": 504}, {"name": "AUSTIC FARM PARTNERS", "county": "Seneca", "coverageLevel": 0.75, "reportedAcres": 1095.5, "grossPremium": 41478, "producerPremium": 8296}, {"name": "AUSTIC FARM PARTNERS", "county": "Schuyler", "coverageLevel": 0.75, "reportedAcres": 46.1, "grossPremium": 1960, "producerPremium": 392}, {"name": "BAINBRIDGE FARM LLC", "county": "Allegany", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "BARBER FARM AGRICULTURE LP", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "BAY FARMS", "county": "Ontario", "coverageLevel": 0.6, "reportedAcres": 473.31, "grossPremium": 13810, "producerPremium": 2761}, {"name": "BAY FARMS", "county": "Yates", "coverageLevel": 0.6, "reportedAcres": 91.92, "grossPremium": 2660, "producerPremium": 825}, {"name": "BAY, MICHAEL", "county": "Ontario", "coverageLevel": 0.5, "reportedAcres": 102.98, "grossPremium": 1810, "producerPremium": 362}, {"name": "BENTLEY, ERIC", "county": "Orleans", "coverageLevel": 0.75, "reportedAcres": 86.4, "grossPremium": 3332, "producerPremium": 666}, {"name": "BRENNEMAN BROTHERS", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": 123.7, "grossPremium": 4348, "producerPremium": 870}, {"name": "BRIAN THOMPSON FARMS INC", "county": "Genesee", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "BRIAN THOMPSON FARMS INC", "county": "Wyoming", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "BRIDGEWATER FARMING LLC", "county": "Williams", "coverageLevel": 0.5, "reportedAcres": 735.62, "grossPremium": 8716, "producerPremium": 1744}, {"name": "BRIDGEWATER FARMING LLC", "county": "Steuben", "coverageLevel": 0.5, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "BROWN, TED", "county": "Centre", "coverageLevel": 0.7, "reportedAcres": 19.0, "grossPremium": 611, "producerPremium": 220}, {"name": "BYLER, STEVEN R", "county": "Mifflin", "coverageLevel": 0.75, "reportedAcres": 74.46, "grossPremium": 1762, "producerPremium": 705}, {"name": "CALLAN FARMS LLC", "county": "Livingston", "coverageLevel": 0.5, "reportedAcres": 315.92, "grossPremium": 4283, "producerPremium": 857}, {"name": "CALLAN FARMS LLC", "county": "Monroe", "coverageLevel": 0.5, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CARLEY FARMS LLC", "county": "Onondaga", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CHASE FARM PARTNERSHIP", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": 214.54, "grossPremium": 9359, "producerPremium": 1873}, {"name": "CHASE FARM PARTNERSHIP", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CLARK FARMS LLC", "county": "Genesee", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CLARK FIVE ENTERPRISES LP", "county": "Centre", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CLARK FIVE ENTERPRISES LP", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CLARK FIVE ENTERPRISES LP", "county": "Huntingdon", "coverageLevel": 0.75, "reportedAcres": 135.15, "grossPremium": 5018, "producerPremium": 1004}, {"name": "CLEMENTS, WES", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CLEMENTS, WES", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CRIPPLE CREEK FARM LLC", "county": "Wyoming", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CRIPPLE CREEK FARM LLC", "county": "Erie", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "CROSS VALLEY FARMS LLC", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "C & W FARM LLC", "county": "Chautauqua", "coverageLevel": 0.6, "reportedAcres": 83.75, "grossPremium": 1938, "producerPremium": 387}, {"name": "C Y FARMS LLC", "county": "Orleans", "coverageLevel": 0.6, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "C Y FARMS LLC", "county": "Monroe", "coverageLevel": 0.6, "reportedAcres": 233.9, "grossPremium": 6944, "producerPremium": 1389}, {"name": "C Y FARMS LLC", "county": "Genesee", "coverageLevel": 0.6, "reportedAcres": 506.2, "grossPremium": 9448, "producerPremium": 1890}, {"name": "DERMODY, DAVID", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 15.0, "grossPremium": 585, "producerPremium": 234}, {"name": "DETWILER, CARL A", "county": "Bedford", "coverageLevel": 0.75, "reportedAcres": 42.06, "grossPremium": 1707, "producerPremium": 342}, {"name": "DEUSENBERY FARM INC", "county": "Steuben", "coverageLevel": 0.75, "reportedAcres": 212.32, "grossPremium": 10649, "producerPremium": 2130}, {"name": "DEUSENBERY FARM INC", "county": "Ontario", "coverageLevel": 0.75, "reportedAcres": 35.4, "grossPremium": 1751, "producerPremium": 700}, {"name": "DEUSENBERY FARM INC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 25.95, "grossPremium": 973, "producerPremium": 194}, {"name": "DISNEY, Michael", "county": "Bedford", "coverageLevel": 0.75, "reportedAcres": 66.14, "grossPremium": 2161, "producerPremium": 414}, {"name": "DISNEY, Michael", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "DISNEY, Michael", "county": "Huntingdon", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "DU MOND AG LLC", "county": "Wayne", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "DU MOND AG LLC", "county": "Seneca", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "DU MOND AG LLC", "county": "Cayuga", "coverageLevel": 0.75, "reportedAcres": 425.4, "grossPremium": 14064, "producerPremium": 2813}, {"name": "DUNHAM FAMILY FARMS", "county": "Orleans", "coverageLevel": 0.65, "reportedAcres": 140.0, "grossPremium": 3752, "producerPremium": 750}, {"name": "EDMUND, NATHANIEL G", "county": "Genesee", "coverageLevel": 0.8, "reportedAcres": 161.02, "grossPremium": 6901, "producerPremium": 2001}, {"name": "EDMUND, NATHANIEL G", "county": "Orleans", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "EDMUND, NATHANIEL G", "county": "Monroe", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "ERNEST L GATES & SONS LLC", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": 147.41, "grossPremium": 6398, "producerPremium": 1855}, {"name": "ERNEST L GATES & SONS LLC", "county": "Wyoming", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "ERNEST L GATES & SONS LLC", "county": "Genesee", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "FIRE CREEK FARMS", "county": "Orleans", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "FIRE CREEK FARMS", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": 76.0, "grossPremium": 3174, "producerPremium": 1206}, {"name": "FREDERICK, MARK", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": 29.0, "grossPremium": 1143, "producerPremium": 457}, {"name": "F & W FARMS LLC", "county": "Ontario", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "GARY SWEDE FARMS INC", "county": "Livingston", "coverageLevel": 0.7, "reportedAcres": 152.6, "grossPremium": 5801, "producerPremium": 1160}, {"name": "GARY SWEDE FARMS INC", "county": "Wyoming", "coverageLevel": 0.7, "reportedAcres": 80.08, "grossPremium": 2609, "producerPremium": 522}, {"name": "GARY SWEDE FARMS INC", "county": "Genesee", "coverageLevel": 0.7, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "GREINER, GEORGE", "county": "Cambria", "coverageLevel": 0.75, "reportedAcres": 48.05, "grossPremium": 1591, "producerPremium": 318}, {"name": "HANSEN FARMS LLC", "county": "Yates", "coverageLevel": 0.65, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "HANSEN FARMS LLC", "county": "Ontario", "coverageLevel": 0.65, "reportedAcres": 442.2, "grossPremium": 15302, "producerPremium": 3060}, {"name": "HARKINS DAIRY FARM LLC", "county": "Wyoming", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "HARKINS DAIRY FARM LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 46.34, "grossPremium": 1451, "producerPremium": 580}, {"name": "HARKINS DAIRY FARM LLC", "county": "Genesee", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "HART FAMILY FARMS LLC", "county": "Juniata", "coverageLevel": 0.75, "reportedAcres": 310.0, "grossPremium": 6282, "producerPremium": 1256}, {"name": "HARTWAY FARMS LLC", "county": "Orleans", "coverageLevel": 0.5, "reportedAcres": 41.38, "grossPremium": 695, "producerPremium": 229}, {"name": "HARTWAY FARMS LLC", "county": "Genesee", "coverageLevel": 0.5, "reportedAcres": 61.35, "grossPremium": 730, "producerPremium": 146}, {"name": "HEIDEMAN FARMS LLC", "county": "Orleans", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "HIDDEN SPRINGS FARM LLC", "county": "Monroe", "coverageLevel": 0.8, "reportedAcres": 114.0, "grossPremium": 5973, "producerPremium": 2926}, {"name": "HITE, ADAM", "county": "Cambria", "coverageLevel": 0.7, "reportedAcres": 88.0, "grossPremium": 2924, "producerPremium": 586}, {"name": "HOWLETT FARMS INC", "county": "Livingston", "coverageLevel": 0.6, "reportedAcres": 301.8, "grossPremium": 6691, "producerPremium": 1338}, {"name": "HOWLETT FARMS INC", "county": "Monroe", "coverageLevel": 0.6, "reportedAcres": 413.7, "grossPremium": 11770, "producerPremium": 2354}, {"name": "HUNSBERGER FARMS LLC", "county": "Cambria", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "HUNSBERGER FARMS LLC", "county": "Somerset", "coverageLevel": 0.75, "reportedAcres": 215.5, "grossPremium": 7285, "producerPremium": 1457}, {"name": "JANIGA, LEONARD", "county": "Wyoming", "coverageLevel": 0.75, "reportedAcres": 76.58, "grossPremium": 2759, "producerPremium": 551}, {"name": "JANIGA, LEONARD", "county": "Erie", "coverageLevel": 0.75, "reportedAcres": 59.2, "grossPremium": 2433, "producerPremium": 973}, {"name": "JOHN B MARTIN & SONS FARMS INC", "county": "Monroe", "coverageLevel": 0.5, "reportedAcres": 409.55, "grossPremium": 8021, "producerPremium": 2647}, {"name": "J RICE FARMS LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 69.0, "grossPremium": 2171, "producerPremium": 434}, {"name": "KAST FARMS INC", "county": "Orleans", "coverageLevel": 0.8, "reportedAcres": 458.8, "grossPremium": 22066, "producerPremium": 6399}, {"name": "KENNEDY FARMS", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 389.78, "grossPremium": 12926, "producerPremium": 5171}, {"name": "KIM MAR FARMS LLC", "county": "Ontario", "coverageLevel": 0.75, "reportedAcres": 57.09, "grossPremium": 3459, "producerPremium": 692}, {"name": "KINGSLEY BROTHERS FARMS LLC", "county": "Genesee", "coverageLevel": 0.8, "reportedAcres": 173.54, "grossPremium": 6696, "producerPremium": 1942}, {"name": "KINGSLEY BROTHERS FARMS LLC", "county": "Wyoming", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "KLUDT BROTHERS INC", "county": "Orleans", "coverageLevel": 0.75, "reportedAcres": 807.61, "grossPremium": 31339, "producerPremium": 6268}, {"name": "KLUDT BROTHERS INC", "county": "Monroe", "coverageLevel": 0.7, "reportedAcres": 299.93, "grossPremium": 14969, "producerPremium": 2994}, {"name": "KOCH LIBRA LLC", "county": "Wyoming", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "KOCH LIBRA LLC", "county": "Monroe", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "KOCH LIBRA LLC", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": 408.45, "grossPremium": 18192, "producerPremium": 5276}, {"name": "KRENZER, DAVID", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": 410.5, "grossPremium": 19002, "producerPremium": 3800}, {"name": "KYPER, ELWOOD", "county": "Huntingdon", "coverageLevel": 0.75, "reportedAcres": 57.23, "grossPremium": 1865, "producerPremium": 746}, {"name": "LAKELAND FARMS LLC", "county": "Yates", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "LAKELAND FARMS LLC", "county": "Ontario", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "LAPE, BENJAMIN", "county": "Somerset", "coverageLevel": 0.75, "reportedAcres": 86.02, "grossPremium": 2899, "producerPremium": 580}, {"name": "LAWNEL FARMS 2 LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 428.97, "grossPremium": 17884, "producerPremium": 7154}, {"name": "LEFEBER FARMS INC", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": 281.67, "grossPremium": 11727, "producerPremium": 3401}, {"name": "LINDER, CRAIG", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "LONG, PAUL", "county": "Cambria", "coverageLevel": 0.75, "reportedAcres": 80.66, "grossPremium": 2954, "producerPremium": 591}, {"name": "LUPINI FARMS", "county": "Columbia", "coverageLevel": 0.7, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "LUPINI FARMS", "county": "Luzerne", "coverageLevel": 0.7, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "LYNN-ETTE & SONS INC", "county": "Orleans", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "LYNN-ETTE & SONS INC", "county": "Genesee", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "LYNN-ETTE & SONS INC", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MCILROY FARMS LLC", "county": "Genesee", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MCILROY FARMS LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 131.3, "grossPremium": 5941, "producerPremium": 1188}, {"name": "MCILROY FARMS LLC", "county": "Wyoming", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MCWILLIAMS, SAMUEL", "county": "Somerset", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MECKLEY, BRIAN", "county": "Dauphin", "coverageLevel": 0.75, "reportedAcres": 10.0, "grossPremium": 205, "producerPremium": 82}, {"name": "MEISENZAHL FARMS LLC", "county": "Ontario", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MEISENZAHL FARMS LLC", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": 184.78, "grossPremium": 9521, "producerPremium": 1904}, {"name": "MEISENZAHL, MICHAEL JOHN", "county": "Ontario", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MEISENZAHL, MICHAEL JOHN", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": 30.29, "grossPremium": 1597, "producerPremium": 639}, {"name": "MEISENZAHL, MICHAEL JOHN", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MERRIMAC FARMS INC", "county": "Livingston", "coverageLevel": 0.7, "reportedAcres": 447.9, "grossPremium": 13906, "producerPremium": 2781}, {"name": "METZLER, BENJAMIN", "county": "York", "coverageLevel": 0.75, "reportedAcres": 46.0, "grossPremium": 986, "producerPremium": 394}, {"name": "METZLER, CRAIG A", "county": "York", "coverageLevel": 0.75, "reportedAcres": 66.46, "grossPremium": 1479, "producerPremium": 296}, {"name": "METZLER, CRAIG A", "county": "Lancaster", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "METZLER, CRAIG A", "county": "Harford", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MROCZEK FARMS", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": 88.32, "grossPremium": 3914, "producerPremium": 1135}, {"name": "MROCZEK FARMS", "county": "Monroe", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MT POCKETS FARMING LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 146.29, "grossPremium": 6232, "producerPremium": 1246}, {"name": "MT POCKETS FARMING LLC", "county": "Genesee", "coverageLevel": 0.75, "reportedAcres": 13.0, "grossPremium": 463, "producerPremium": 93}, {"name": "MULLIGAN FAMILY FARM LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 93.38, "grossPremium": 3844, "producerPremium": 769}, {"name": "MULLIGAN FAMILY FARM LLC", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MY-T ACRES INC", "county": "Orleans", "coverageLevel": 0.7, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MY-T ACRES INC", "county": "Genesee", "coverageLevel": 0.7, "reportedAcres": 271.71, "grossPremium": 7691, "producerPremium": 2769}, {"name": "MY-T ACRES INC", "county": "Livingston", "coverageLevel": 0.7, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "MY-T ACRES INC", "county": "Monroe", "coverageLevel": 0.7, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "NAAS FARMS LLC", "county": "Erie", "coverageLevel": 0.8, "reportedAcres": 110.0, "grossPremium": 4316, "producerPremium": 1252}, {"name": "NAAS FARMS LLC", "county": "Orleans", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "NAAS FARMS LLC", "county": "Genesee", "coverageLevel": 0.8, "reportedAcres": 95.5, "grossPremium": 3900, "producerPremium": 1131}, {"name": "NAGLE JR, THOMAS", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "NAGLE JR, THOMAS", "county": "Cambria", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "NICEWONGER JR, GARY", "county": "Westmoreland", "coverageLevel": 0.7, "reportedAcres": 49.9, "grossPremium": 1468, "producerPremium": 294}, {"name": "NICEWONGER JR, GARY", "county": "Indiana", "coverageLevel": 0.7, "reportedAcres": 101.1, "grossPremium": 3133, "producerPremium": 627}, {"name": "PADGHAM, RICHARD", "county": "Wayne", "coverageLevel": 0.65, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "PADGHAM, RICHARD", "county": "Ontario", "coverageLevel": 0.65, "reportedAcres": 79.0, "grossPremium": 2790, "producerPremium": 558}, {"name": "PAGEN FARMS INC", "county": "Genesee", "coverageLevel": 0.75, "reportedAcres": 62.6, "grossPremium": 2088, "producerPremium": 418}, {"name": "PAGEN FARMS INC", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "PALLADINO AND CARLEY FARMS LLC", "county": "Onondaga", "coverageLevel": 0.75, "reportedAcres": 117.45, "grossPremium": 4224, "producerPremium": 1689}, {"name": "PEPPER, LUCAS", "county": "Bradford", "coverageLevel": 0.7, "reportedAcres": 71.9, "grossPremium": 1470, "producerPremium": 294}, {"name": "PHILLIPS FARMS LLC", "county": "Dauphin", "coverageLevel": 0.7, "reportedAcres": 36.34, "grossPremium": 632, "producerPremium": 228}, {"name": "PINGREY FARM 2 LLC", "county": "Wyoming", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "RANDY & KRIS SMITH", "county": "Bedford", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "RANDY & KRIS SMITH", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "RANNEY FARMS LLC", "county": "Niagara", "coverageLevel": 0.8, "reportedAcres": 138.3, "grossPremium": 7020, "producerPremium": 2036}, {"name": "RAUBERDALE FARMS INC", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "RAUBERDALE FARMS INC", "county": "Steuben", "coverageLevel": 0.8, "reportedAcres": 172.92, "grossPremium": 10784, "producerPremium": 3128}, {"name": "RAY SICK FARMS AND EXCAVATING LLC", "county": "Ontario", "coverageLevel": 0.5, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "RAY SICK FARMS AND EXCAVATING LLC", "county": "Livingston", "coverageLevel": 0.5, "reportedAcres": 104.0, "grossPremium": 1454, "producerPremium": 291}, {"name": "RICE, CRAIG", "county": "Livingston", "coverageLevel": 0.5, "reportedAcres": 80.0, "grossPremium": 1157, "producerPremium": 231}, {"name": "RICH HARVEST FARMS LLC", "county": "Livingston", "coverageLevel": 0.5, "reportedAcres": 309.8, "grossPremium": 4045, "producerPremium": 810}, {"name": "RICH HARVEST FARMS LLC", "county": "Ontario", "coverageLevel": 0.5, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "RITTER, MERRILEE", "county": "Genesee", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "RL JEFFRES & SON INC", "county": "Wyoming", "coverageLevel": 0.75, "reportedAcres": 835.9, "grossPremium": 41394, "producerPremium": 8278}, {"name": "RL JEFFRES & SON INC", "county": "Genesee", "coverageLevel": 0.75, "reportedAcres": 351.9, "grossPremium": 14511, "producerPremium": 2902}, {"name": "RL JEFFRES & SON INC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 925.6, "grossPremium": 33414, "producerPremium": 6682}, {"name": "ROBERT ORR", "county": "Cattaraugus", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "ROBERT ORR", "county": "Chautauqua", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "ROGERS FARMS LLC", "county": "Ontario", "coverageLevel": 0.5, "reportedAcres": 415.77, "grossPremium": 7633, "producerPremium": 1526}, {"name": "ROHRBAUGH FARMS LLC", "county": "Somerset", "coverageLevel": 0.8, "reportedAcres": 420.38, "grossPremium": 15513, "producerPremium": 4487}, {"name": "ROHRBAUGH FARMS LLC", "county": "Fayette", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "SCHOFIELD FARMS LLC", "county": "Chautauqua", "coverageLevel": 0.75, "reportedAcres": 576.6, "grossPremium": 18243, "producerPremium": 3647}, {"name": "SCHOFIELD FARMS LLC", "county": "Erie", "coverageLevel": 0.75, "reportedAcres": 67.3, "grossPremium": 3791, "producerPremium": 758}, {"name": "SHADE TREE FARM LLC", "county": "Genesee", "coverageLevel": 0.8, "reportedAcres": 36.77, "grossPremium": 1687, "producerPremium": 489}, {"name": "SHADE TREE FARM LLC", "county": "Monroe", "coverageLevel": 0.8, "reportedAcres": 36.36, "grossPremium": 2553, "producerPremium": 1251}, {"name": "SHAFFER PRODUCTIONS INC", "county": "Northumberland", "coverageLevel": 0.75, "reportedAcres": 452.72, "grossPremium": 11367, "producerPremium": 2274}, {"name": "SHAFFER PRODUCTIONS INC", "county": "Dauphin", "coverageLevel": 0.75, "reportedAcres": 37.5, "grossPremium": 605, "producerPremium": 242}, {"name": "SHAFFER, SCOTT", "county": "Fulton", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "SHAFFER, SCOTT", "county": "Bedford", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "SILCO FARMS LLC", "county": "Ontario", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "SILCO FARMS LLC", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "SILSBY SETTLEMENT FARMS LLC", "county": "Niagara", "coverageLevel": 0.75, "reportedAcres": 240.92, "grossPremium": 12126, "producerPremium": 2425}, {"name": "SKEBECK FARMS", "county": "Cambria", "coverageLevel": 0.75, "reportedAcres": 158.24, "grossPremium": 6554, "producerPremium": 1309}, {"name": "SMITH, BERNARD D", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": 107.25, "grossPremium": 3842, "producerPremium": 1537}, {"name": "SMITH, BERNARD D", "county": "Huntingdon", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "SMITH, GERALD E", "county": "Bedford", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "SMITH, GERALD E", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": 78.47, "grossPremium": 2848, "producerPremium": 569}, {"name": "STEWART, GREGORY", "county": "Harford", "coverageLevel": 0.65, "reportedAcres": 174.91, "grossPremium": 2132, "producerPremium": 426}, {"name": "STOKOE 1812 INC", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": 241.59, "grossPremium": 12253, "producerPremium": 2451}, {"name": "STOKOE 1812 INC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 141.6, "grossPremium": 5936, "producerPremium": 1188}, {"name": "STRAPP FARM LLC", "county": "Ontario", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "STULTZ, ROBERT", "county": "Blair", "coverageLevel": 0.75, "reportedAcres": 17.0, "grossPremium": 633, "producerPremium": 127}, {"name": "SUGAR BRANCH FARMS LLC", "county": "Bradford", "coverageLevel": 0.75, "reportedAcres": 307.82, "grossPremium": 7386, "producerPremium": 1478}, {"name": "SUGAR BRANCH FARMS LLC", "county": "Tioga", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "SULLIVAN FARMS LLC", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": 367.1, "grossPremium": 16043, "producerPremium": 4652}, {"name": "SULLIVAN FARMS LLC", "county": "Monroe", "coverageLevel": 0.8, "reportedAcres": 115.61, "grossPremium": 5955, "producerPremium": 1726}, {"name": "SUNRISE AG LLC", "county": "Cayuga", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "T & D CORCORAN FARMS & SON LLC", "county": "Wyoming", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "T & D CORCORAN FARMS & SON LLC", "county": "Genesee", "coverageLevel": 0.8, "reportedAcres": 213.72, "grossPremium": 8668, "producerPremium": 2514}, {"name": "T & D CORCORAN FARMS & SON LLC", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": 90.44, "grossPremium": 3626, "producerPremium": 1450}, {"name": "T & D CORCORAN FARMS & SON LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 69.0, "grossPremium": 2588, "producerPremium": 1035}, {"name": "THORNAPPLE DAIRY LLC", "county": "Wyoming", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "THORNAPPLE DAIRY LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "TRAVIS, JOHN", "county": "Erie", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "TRAVIS, JOHN", "county": "Wyoming", "coverageLevel": 0.8, "reportedAcres": 78.3, "grossPremium": 3527, "producerPremium": 1023}, {"name": "TRI ACRES LLC", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 302.15, "grossPremium": 11336, "producerPremium": 2268}, {"name": "VERRATTI FARMS LLC", "county": "Orleans", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "VERRATTI FARMS LLC", "county": "Niagara", "coverageLevel": 0.75, "reportedAcres": 342.5, "grossPremium": 15709, "producerPremium": 3143}, {"name": "VOELPEL FARMS INC", "county": "Niagara", "coverageLevel": 0.75, "reportedAcres": 506.8, "grossPremium": 22364, "producerPremium": 4473}, {"name": "WALTONS WAY ANGUS FARM", "county": "Livingston", "coverageLevel": 0.75, "reportedAcres": 67.7, "grossPremium": 2336, "producerPremium": 934}, {"name": "WALTONS WAY ANGUS FARM", "county": "Monroe", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "WASIK FARMS", "county": "Niagara", "coverageLevel": 0.75, "reportedAcres": 534.57, "grossPremium": 24842, "producerPremium": 4967}, {"name": "WILSON, BRADLEY T", "county": "Mercer", "coverageLevel": 0.75, "reportedAcres": 74.3, "grossPremium": 4618, "producerPremium": 924}, {"name": "WILSON, BRADLEY T", "county": "Venango", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "WILSON, BRADLEY T", "county": "Clarion", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "WILSON, BRADLEY T", "county": "Butler", "coverageLevel": 0.75, "reportedAcres": 67.7, "grossPremium": 2586, "producerPremium": 517}, {"name": "WILSON, BRADLEY T", "county": "Lawrence", "coverageLevel": 0.75, "reportedAcres": 229.8, "grossPremium": 6372, "producerPremium": 1274}, {"name": "WILSON, BRADLEY T", "county": "Armstrong", "coverageLevel": 0.75, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "WOODLAWN FARMS INC BY JOHN MORGAN", "county": "Livingston", "coverageLevel": 0.8, "reportedAcres": 91.68, "grossPremium": 4362, "producerPremium": 1266}, {"name": "YAHNER BROTHERS FARMS LLC", "county": "Cambria", "coverageLevel": 0.75, "reportedAcres": 384.67, "grossPremium": 16346, "producerPremium": 16346}, {"name": "ZELIFF FARMS LLC", "county": "Orleans", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}, {"name": "ZELIFF FARMS LLC", "county": "Niagara", "coverageLevel": 0.8, "reportedAcres": null, "grossPremium": null, "producerPremium": null}];

function WheatSpotCheckScreen({ farmers }) {
  const norm = s => (s||'').toUpperCase().trim();
  const [filter, setFilter] = useState('issues');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    return WHEAT_SHEET.map(sr => {
      const snorm = norm(sr.name);
      const cnorm = norm(sr.county);

      // Find farmer in loaded data by name (fuzzy uppercase match)
      const farmer = farmers.find(f => norm(f.name) === snorm);
      const farmerExists = !!farmer;

      // Get their 2026 wheat yields for this county (non-ECO RP only)
      const dbRows = farmerExists
        ? (farmer.approvedYields||[]).filter(a =>
            a.crop === 'WHEAT' &&
            norm(a.county) === cnorm &&
            a.plan && !a.plan.includes('ECO')
          )
        : [];

      // Also check if farmer has wheat at all (any county)
      const hasAnyWheat = farmerExists && (farmer.approvedYields||[]).some(a => a.crop === 'WHEAT');

      const issues = [];
      let status = 'ok';

      if (!farmerExists) {
        status = 'missing';
        issues.push('Farmer not in app');
      } else if (!hasAnyWheat) {
        status = 'missing';
        issues.push('Farmer exists but no wheat policies imported');
      } else if (dbRows.length === 0) {
        status = 'missing_county';
        issues.push(`No wheat row for ${sr.county} county`);
      } else {
        const dbCov = dbRows[0].coverageLevel;
        if (dbCov != null && Math.abs(dbCov - sr.coverageLevel) > 0.01) {
          status = 'warn';
          issues.push(`Coverage: DB ${(dbCov*100).toFixed(0)}% vs Sheet ${(sr.coverageLevel*100).toFixed(0)}%`);
        }
        if (sr.reportedAcres != null) {
          const dbAcres = dbRows.reduce((s,r) => s + (Number(r.acres)||0), 0);
          if (dbAcres === 0) {
            if (status==='ok') status = 'warn';
            issues.push(`No acres in app (sheet: ${sr.reportedAcres} ac)`);
          } else {
            const pct = Math.abs(dbAcres - sr.reportedAcres) / sr.reportedAcres;
            if (pct > 0.05) {
              if (status==='ok') status = 'warn';
              issues.push(`Acres: app ${dbAcres.toFixed(2)} vs sheet ${sr.reportedAcres} (${(pct*100).toFixed(1)}% off)`);
            }
          }
        }
        if (issues.length === 0) issues.push('✓ Match');
      }

      const dbAcres = dbRows.reduce((s,r) => s + (Number(r.acres)||0), 0);
      const dbAph   = dbRows.length > 0 ? dbRows[0].approvedYield : null;
      const dbCov   = dbRows.length > 0 ? dbRows[0].coverageLevel : null;
      return { sr, dbRows, dbAcres, dbAph, dbCov, status, issues, farmerExists, hasAnyWheat };
    });
  }, [farmers]);

  const counts = { ok: rows.filter(r=>r.status==='ok').length, warn: rows.filter(r=>r.status==='warn').length, missing_county: rows.filter(r=>r.status==='missing_county').length, missing: rows.filter(r=>r.status==='missing').length };
  const missingFarmers = [...new Set(rows.filter(r=>r.status==='missing').map(r=>r.sr.name))];

  const visible = rows.filter(r => {
    if (filter==='ok') return r.status==='ok';
    if (filter==='issues') return r.status!=='ok';
    if (filter==='missing') return r.status==='missing';
    if (filter==='warn') return r.status==='warn'||r.status==='missing_county';
    return true;
  }).filter(r => !search || norm(r.sr.name).includes(norm(search)) || norm(r.sr.county).includes(norm(search)));

  const TH = { padding:'8px 10px', background:'#2a1e00', color:'#f4e8c0', fontWeight:700, fontSize:11, textAlign:'left', letterSpacing:'0.05em', whiteSpace:'nowrap' };
  const TD = { padding:'7px 10px', borderBottom:'1px solid #f0ede8', fontSize:12, verticalAlign:'middle' };
  const badge = (s) => {
    const cfg = { ok:{bg:'#e8f5e8',c:'#2a7a2a',t:'✓ OK'}, warn:{bg:'#fdf8e0',c:'#8a6a00',t:'⚠ Check'}, missing_county:{bg:'#fff0e0',c:'#a05000',t:'⚠ No County'}, missing:{bg:'#fde8e8',c:'#cc3333',t:'✗ Missing'} };
    const d = cfg[s]||cfg.ok;
    return <span style={{ background:d.bg, color:d.c, padding:'2px 8px', borderRadius:10, fontWeight:800, fontSize:10, whiteSpace:'nowrap' }}>{d.t}</span>;
  };

  return (
    <div style={{ padding:'16px 16px 100px', maxWidth:1100, margin:'0 auto' }}>
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:'#1a2e1a' }}>🌾 Wheat Spot Check</div>
        <div style={{ fontSize:12, color:'#60735f', marginTop:2 }}>Agency Root spreadsheet vs app data · 221 rows · 127 farmers</div>
      </div>

      {/* Summary cards */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
        {[
          { label:'✓ Matching', val:counts.ok, color:'#2a7a2a', bg:'#e8f5e8' },
          { label:'⚠ Need Attention', val:counts.warn, color:'#8a6a00', bg:'#fdf8e0' },
          { label:'County Missing', val:counts.missing_county, color:'#a05000', bg:'#fff0e0' },
          { label:'Farmers Not Found', val:missingFarmers.length, color:'#cc3333', bg:'#fde8e8' },
        ].map(c => (
          <div key={c.label} style={{ background:c.bg, border:`1px solid ${c.color}22`, borderRadius:8, padding:'12px 18px', minWidth:130 }}>
            <div style={{ fontSize:26, fontWeight:900, color:c.color }}>{c.val}</div>
            <div style={{ fontSize:11, color:c.color, fontWeight:600, marginTop:2 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Missing farmers callout */}
      {missingFarmers.length > 0 && (
        <div style={{ background:'#fde8e8', border:'1px solid #cc333344', borderRadius:8, padding:'12px 16px', marginBottom:16 }}>
          <div style={{ fontWeight:800, color:'#cc3333', fontSize:13, marginBottom:6 }}>Farmers not found in app ({missingFarmers.length}) — need to be imported:</div>
          <div style={{ fontSize:12, color:'#aa2222', lineHeight:1.8 }}>{missingFarmers.join(' · ')}</div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
        {[['all','All',rows.length],['issues','Issues Only',rows.filter(r=>r.status!=='ok').length],['missing','Not Found',counts.missing],['warn','Warnings',counts.warn+counts.missing_county],['ok','OK',counts.ok]].map(([k,l,n])=>(
          <button key={k} onClick={()=>setFilter(k)} style={{ padding:'5px 14px', borderRadius:16, border:'1px solid', borderColor:filter===k?'#1a2e1a':'#ccc', background:filter===k?'#1a2e1a':'#fff', color:filter===k?'#fff':'#444', fontSize:12, fontWeight:600, cursor:'pointer' }}>{l} ({n})</button>
        ))}
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name or county..." style={{ padding:'5px 12px', border:'1px solid #ccc', borderRadius:16, fontSize:12, width:200, marginLeft:'auto' }} />
      </div>

      {/* Table */}
      <div style={{ overflowX:'auto', borderRadius:10, border:'1px solid #e0ddd8' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr>{['Status','Farmer','County','Sheet Cov%','App Cov%','Sheet Acres','App Acres','App APH','Notes'].map(h=><th key={h} style={TH}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {visible.map((r,i) => (
              <tr key={i} style={{ background: i%2===0?'#fff':'#fafaf8' }}>
                <td style={TD}>{badge(r.status)}</td>
                <td style={{...TD, fontWeight:700, maxWidth:200}}>{r.sr.name}</td>
                <td style={TD}>{r.sr.county}</td>
                <td style={{...TD, fontVariantNumeric: "tabular-nums"}}>{(r.sr.coverageLevel*100).toFixed(0)}%</td>
                <td style={{...TD, fontVariantNumeric: "tabular-nums"}}>{r.dbCov!=null?(r.dbCov*100).toFixed(0)+'%':<span style={{color:'#bbb'}}>—</span>}</td>
                <td style={{...TD, fontVariantNumeric: "tabular-nums"}}>{r.sr.reportedAcres!=null?r.sr.reportedAcres.toLocaleString():<span style={{color:'#bbb'}}>not yet</span>}</td>
                <td style={{...TD, fontVariantNumeric: "tabular-nums"}}>{r.dbAcres>0?r.dbAcres.toFixed(2):<span style={{color:'#bbb'}}>—</span>}</td>
                <td style={{...TD, fontVariantNumeric: "tabular-nums"}}>{r.dbAph!=null?r.dbAph+' bu':<span style={{color:'#bbb'}}>—</span>}</td>
                <td style={{...TD, color:'#666', fontSize:11}}>{r.issues.join('; ')}</td>
              </tr>
            ))}
            {visible.length===0&&<tr><td colSpan={9} style={{...TD,textAlign:'center',color:'#aaa',padding:24}}>No rows match filter</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MANAGE POLICIES SCREEN
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// GRAIN MARKETING VIEW
// ─────────────────────────────────────────────────────────────────────────────

const GRAIN_CROPS = ['Corn', 'Soybeans', 'Wheat'];
// Grain-tool crop label → USDA futures board commodity key (api/_futures.js).
const GRAIN_CROP_TO_COMMODITY = { Corn: 'corn', Soybeans: 'soybeans', Wheat: 'wheat' };
// CME delivery-month codes. Lets us name a board the way the basis sheet does: a board with
// mo=9,yr=2026 is 'U26', which is exactly what grain_basis_board.cme_contract carries — so a
// posted basis can be matched to the board it's actually quoted off, without string-surgery
// on the contract symbol (roots aren't all one letter).
const CME_MONTH_CODE = { 1: 'F', 2: 'G', 3: 'H', 4: 'J', 5: 'K', 6: 'M', 7: 'N', 8: 'Q', 9: 'U', 10: 'V', 11: 'X', 12: 'Z' };

const GRAIN_COLORS = { Corn: '#c8860a', Soybeans: '#3a6e32', Wheat: '#6b4f00' };
// ORGANIC CORN is CORN on every chart that carries a colour, so it inherits the
// base crop's hue rather than getting one of its own — a distinct colour would
// read as a fourth commodity when it is the same commodity under a different
// practice. GRAIN_COLORS is keyed 'Corn'/'Soybeans'/'Wheat' (title case) while a
// marketing crop arrives upper case, so match case-insensitively rather than
// adding a second spelling of each key.
// ORGANIC IS A PRACTICE, NOT A COMMODITY, AND THE BOARD KNOWS ONLY THE
// COMMODITY. There is no organic corn futures contract — an organic grower
// hedges on the same ZC as everybody else — so every lookup that is about the
// COMMODITY (the futures contract, its delivery months, the posted basis, the
// cost-of-production sheet) resolves the base crop. Only the lookups that are
// about the PRACTICE (acres, APH, guarantee, reported production) split, and
// those are the ones this book actually holds separately.
//
// Without this an organic row falls through every one of those maps: getFutures
// returns the hardcoded 4.50 fallback and the whole position marks against a
// price that is not a price.
function grainBaseCrop(marketingCrop) {
  const t = String(marketingCrop || '').trim().toUpperCase();
  const base = t.startsWith('ORGANIC ') ? t.slice(8).trim() : t;
  for (const k of GRAIN_CROPS) if (k.toUpperCase() === base) return k;
  return marketingCrop;
}
function grainCropColor(marketingCrop) {
  return GRAIN_COLORS[grainBaseCrop(marketingCrop)] || '#60735f';
}
// PROGRAM CHIP. A specialty contract stays under its board crop so the bushels
// count toward the position and the guarantee; this chip is the only thing on
// the row that says it does not price like the plain crop. See
// grainProgramFromCommodity — Jeffres' Plenish beans sit 120c off his
// conventional beans on the same delivery window.
const grainProgramChip = (p) => !p ? null : (
  <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 10.5, fontWeight: 800,
                 background: '#efe6fa', color: '#5a3a8a', border: '1px solid #d9c8ee',
                 whiteSpace: 'nowrap', verticalAlign: 'middle' }}
        title={`${p} — a specialty program, not a separate crop. Counted as ${'its board crop'} against the guarantee and % sold; priced on its own basis.`}>{p}</span>
);
const GRAIN_BG     = { Corn: '#fef9ec', Soybeans: '#eef6ec', Wheat: '#f8f4ec' };

// The delivery/contract months that make up a crop's marketing year are generated
// from the CME contract months (FUTURES_MONTHS_BY_CROP) and each crop's new-crop
// starting contract - NOT hand-maintained per year. A marketing year runs from the
// new-crop month forward through the contract cycle, wrapping into the next calendar
// year, up to (but not including) the next new-crop month.
//   Corn  -> Dec Y, then Mar/May/Jul/Sep Y+1
//   Beans -> Nov Y, then Jan/Mar/May/Jul/Aug/Sep Y+1
//   Wheat -> Jul Y, Sep Y, Dec Y, then Mar/May Y+1
const NEW_CROP_MONTH = { Corn: 'Dec', Soybeans: 'Nov', Wheat: 'Jul' };
const _MONTH_NUM = { Jan:1, Feb:2, Mar:3, Apr:4, May:5, Jun:6, Jul:7, Aug:8, Sep:9, Oct:10, Nov:11, Dec:12 };
function deliveryMonthsForCropYear(crop, cropYear) {
  const months = FUTURES_MONTHS_BY_CROP[crop];
  const start = NEW_CROP_MONTH[crop];
  const yr0 = Number(cropYear);
  if (!months || !start || !yr0) return [];
  const startIdx = months.indexOf(start);
  if (startIdx < 0) return [];
  const out = [];
  let yr = yr0, prevNum = -Infinity;
  for (let i = 0; i < months.length; i++) {
    const mon = months[(startIdx + i) % months.length];
    const num = _MONTH_NUM[mon];
    if (num < prevNum) yr += 1;
    out.push(mon + ' ' + String(yr).slice(-2));
    prevNum = num;
  }
  return out;
}
// Same shape as the old hand-maintained table (GRAIN_DELIVERY_MONTHS[2026].Corn),
// but generated per crop year on access, so the forward curve stays correct every
// marketing year with no edits.
const GRAIN_DELIVERY_MONTHS = new Proxy({}, {
  get: (_t, yearKey) => {
    const year = Number(yearKey);
    if (!year) return undefined;
    return {
      Corn:     deliveryMonthsForCropYear('Corn', year),
      Soybeans: deliveryMonthsForCropYear('Soybeans', year),
      Wheat:    deliveryMonthsForCropYear('Wheat', year),
    };
  },
});

const GRAIN_DEFAULT_FUTURES = {
  2025: { Corn: { price: 4.35, month: 'Dec 25' }, Soybeans: { price: 9.72, month: 'Nov 25' }, Wheat: { price: 5.44, month: 'Jul 25' } },
  2026: { Corn: { price: 4.52, month: 'Dec 26' }, Soybeans: { price: 9.88, month: 'Nov 26' }, Wheat: { price: 5.60, month: 'Jul 26' } },
  2027: { Corn: { price: 4.65, month: 'Dec 27' }, Soybeans: { price: 10.05, month: 'Nov 27' }, Wheat: { price: 5.72, month: 'Jul 27' } },
};

// CME contract month codes — standard exchange convention.
// Corn (ZC) and SRW Wheat (ZW) trade Mar/May/Jul/Sep/Dec only.
// Soybeans (ZS) trade Jan/Mar/May/Jul/Aug/Sep/Nov.
// (Plenish/specialty soybean contracts price off the same ZS contract.)
const FUTURES_MONTH_CODES = {
  F: 'Jan', G: 'Feb', H: 'Mar', J: 'Apr', K: 'May', M: 'Jun',
  N: 'Jul', Q: 'Aug', U: 'Sep', V: 'Oct', X: 'Nov', Z: 'Dec',
};
const MONTH_NAME_TO_CODE = Object.fromEntries(
  Object.entries(FUTURES_MONTH_CODES).map(([code, name]) => [name, code])
);
const FUTURES_MONTHS_BY_CROP = {
  Corn:     ['Mar', 'May', 'Jul', 'Sep', 'Dec'],
  Wheat:    ['Mar', 'May', 'Jul', 'Sep', 'Dec'],
  Soybeans: ['Jan', 'Mar', 'May', 'Jul', 'Aug', 'Sep', 'Nov'],
};
const COMMODITY_TO_CROP = {
  CORN: 'Corn', WHEAT: 'Wheat', SOYBEANS: 'Soybeans',
  'PLENISH SOYBEANS': 'Soybeans', 'NON-GMO SOYBEANS': 'Soybeans',
  OATS: 'Oats',
};
// AN ELEVATOR NAMES ITS OWN GRADE, AND AN EXACT-MATCH TABLE MISSES EVERY ONE.
// COMMODITY_TO_CROP was a lookup with a title-case fallback, so Edgewood's
// "Wheat-S.Red Winter" stored as the crop `Wheat-s.red winter` on all 6 of its
// wheat contracts and " Plenish Beans" as `Plenish beans` — labels nothing in
// the app can match, so those contracts join no position, no guarantee and no
// % sold. It also corrupted the CROP YEAR: _NEW_CROP_MONTH_FOR_CROP is keyed by
// crop, so an unknown label silently took corn's Oct-1 rule and put July-2026
// wheat in CY2025.
//
// The table still wins where it speaks. Past it the rule is a PREFIX/marker
// test, not a fuzzy match, and it is deliberately narrow:
//   • PLENISH is a soybean trait, already sourced in BOARD_COMMODITY_TO_CROP —
//     it is what catches "Plenish Beans", which carries no "soy" at all.
//   • bare BEANS alone is soybeans; "DRY BEANS" (types 303-313, a different
//     insured crop entirely) must NEVER fold into soybeans, so only the whole
//     word on its own qualifies.
// Anything else keeps the old title-case passthrough — an unrecognised
// commodity reads as itself rather than being guessed into a crop we trade.
function grainCropFromCommodity(commodity) {
  const raw = String(commodity ?? '').trim();
  if (!raw) return null;
  const u = raw.toUpperCase();
  if (COMMODITY_TO_CROP[u]) return COMMODITY_TO_CROP[u];
  if (/^WHEAT\b/.test(u) || /^WINTER WHEAT\b/.test(u)) return 'Wheat';
  if (/^CORN\b/.test(u)) return 'Corn';
  if (u.includes('SOY') || u.includes('PLENISH') || u === 'BEANS') return 'Soybeans';
  if (/^OATS?\b/.test(u)) return 'Oats';
  if (/^BARLEY\b/.test(u)) return 'Barley';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}
// THE PROGRAM THE CROP LABEL WOULD OTHERWISE THROW AWAY.
//
// Plenish is a high-oleic SOYBEAN — soybeans against the RMA guarantee, grown on
// soybean acres, delivered as soybeans — so `crop` must stay 'Soybeans' or the
// bushels count toward no position and no % sold, which is exactly what the old
// importer did by storing crop `Plenish beans`.
//
// But it is a contracted specialty and it PRICES DIFFERENTLY, so the label is
// load-bearing rather than decorative. Measured on R L Jeffres' own CY2026
// Edgewood contracts, the two soybean lines are **120 cents apart in basis** —
// conventional `0110162A` at −0.45 against Plenish `00041535` at **+0.75**, same
// delivery window. Blend them and the average describes neither.
//
// This is the shape the Cash Board already settled: `grain_basis_board` carries
// `plenish` as its own commodity with its own posted columns, and the basis
// history stores it as SOYBEANS on a `plenish_*` slug — in its own words, "what
// keeps a Plenish premium out of a standard soybean basis". Contracts now match.
//
// ONLY SOURCED PROGRAMS GET A NAME. These are the strings the files and
// COMMODITY_TO_CROP actually carry; anything else returns null and renders as
// nothing. **A null is "no program stated", never "conventional confirmed"** —
// an absence is not a determination, and nothing here is ever inferred from a
// price. A premium basis is evidence of a premium, not of WHICH program.
function grainProgramFromCommodity(commodity) {
  const u = String(commodity ?? '').trim().toUpperCase();
  if (!u) return null;
  if (u.includes('PLENISH')) return 'Plenish';
  if (u.includes('NON-GMO') || u.includes('NON GMO') || u.includes('NONGMO')) return 'Non-GMO';
  return null;
}
const CROP_TO_FUTURES_PREFIX = { Corn: 'ZC', Wheat: 'ZW', Soybeans: 'ZS' };
const FUTURES_PREFIX_TO_CROP = { ZC: 'Corn', ZW: 'Wheat', ZS: 'Soybeans' };

// Parse "ZCN27" → { crop:'Corn', monthName:'Jul', monthCode:'N', year:2027, display:'Jul 27' }
// Returns null if the symbol doesn't match the expected pattern.
function parseFuturesSymbol(symbol) {
  if (!symbol) return null;
  const s = String(symbol).toUpperCase().trim();
  const m = s.match(/^(ZC|ZW|ZS)([FGHJKMNQUVXZ])(\d{2})$/);
  if (!m) return null;
  const [, prefix, monthCode, yr2] = m;
  const monthName = FUTURES_MONTH_CODES[monthCode];
  const year = 2000 + parseInt(yr2, 10);
  return {
    crop: FUTURES_PREFIX_TO_CROP[prefix],
    monthName, monthCode,
    year,
    display: `${monthName} ${yr2}`,
    symbol: s,
  };
}

// Build "ZCN27" from crop + monthName + year (or 4-digit year).
function buildFuturesSymbol(crop, monthName, year) {
  const prefix = CROP_TO_FUTURES_PREFIX[crop];
  const code = MONTH_NAME_TO_CODE[monthName];
  if (!prefix || !code || !year) return null;
  const yr2 = String(year).slice(-2);
  return `${prefix}${code}${yr2}`;
}

// Display-friendly month label like "Jul 27" from monthName + year.
function fmonthDisplay(monthName, year) {
  if (!monthName || !year) return '';
  return `${monthName} ${String(year).slice(-2)}`;
}

// "Jul 27" → sortable ordinal (year*12 + monthIndex). Null if unparseable.
// Used to match / find-nearest a contract's month against the forward curve.
const _MON3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthLabelToOrd(label) {
  const m = String(label || '').trim().match(/^([A-Za-z]{3,})\.?\s*'?(\d{2,4})$/);
  if (!m) return null;
  const mon3 = m[1].slice(0, 3).toLowerCase();
  const monIdx = _MON3.findIndex(x => x.toLowerCase() === mon3);
  if (monIdx < 0) return null;
  let yr = parseInt(m[2], 10);
  if (yr < 100) yr += 2000;
  return yr * 12 + monIdx;
}
// The forward-curve month a contract should map to: its explicit display month,
// else parsed from its futures symbol (ZCN27 → "Jul 27"), else its ship month.
function contractTargetMonth(c) {
  const disp = String(c.futures_month || c.fmonth || '').replace(/\s*(Corn|Wheat|Soybeans)$/i, '').trim();
  if (monthLabelToOrd(disp) != null) return disp;
  const sym = parseFuturesSymbol(c.futures_symbol || c.futures_reference);
  if (sym?.display) return sym.display;
  if (monthLabelToOrd(c.ship_month) != null) return c.ship_month;
  return null;
}

// Contract-type set used in dropdowns + the pill UI.
const GRAIN_CONTRACT_TYPES = ['Fixed Price', 'Basis', 'HTA', 'Bushels Only'];
const GRAIN_TYPE_DESCRIPTIONS = {
  'Fixed Price':  'Cash price set — futures + basis both locked at contract creation.',
  'Basis':        'Basis locked, futures float — sub-priced over time as futures get fixed.',
  'HTA':          'Hedge-to-Arrive — futures locked, basis fixes later at delivery.',
  'Bushels Only': 'Bushels committed, no price set yet (open / spec).',
};
// Visual palette for contract types — used for donut segments + legend.
const GRAIN_TYPE_COLORS = {
  'Fixed Price':  '#2d5a10',
  'HTA':          '#1a4a7a',
  'Basis':        '#c8860a',
  'Bushels Only': '#7a4ab0',
};

// Derive the CROP YEAR a contract belongs to. Per Kevin's spec, the
// new-crop-month (when CY Y starts) varies by crop because harvest
// timing differs:
//   • Corn new crop starts Oct 1 → CY Y = ship 10/1/Y through 9/30/(Y+1)
//   • Soybeans new crop starts Sep 1 (harvest begins Sept; USDA marketing
//     year is Sept 1) → CY Y = ship 9/1/Y through 8/31/(Y+1)
//   • Wheat new crop starts Jul 1 (winter wheat is combined in July)
//     → CY Y = ship 7/1/Y through 6/30/(Y+1)
//
// Priority of inputs:
//   1. ship_month (the explicit "Oct 26" / "Nov 26" entry on the contract)
//   2. delivery_start (date — fall back when ship_month wasn't entered)
//   3. futures_symbol (futures contract month code)
const _MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const _FUTURES_CODE_TO_MONTH_NUM = { F:1, G:2, H:3, J:4, K:5, M:6, N:7, Q:8, U:9, V:10, X:11, Z:12 };
// Soybeans start coming off in September (USDA soybean marketing year begins
// Sept 1), so a Sept harvest-delivery is the CURRENT year's crop. Corn new crop
// Oct 1, wheat JUL 1 (winter wheat is combined in July, so the crop does not
// exist to ship until then).
const _NEW_CROP_MONTH_FOR_CROP = { Corn: 10, Soybeans: 9, Wheat: 7 };
function _cropYearFromMonthYear(month, year, crop) {
  const newCropMonth = _NEW_CROP_MONTH_FOR_CROP[crop] || 10;
  return month >= newCropMonth ? year : year - 1;
}
function deriveContractCropYear({ ship_month, futures_symbol, delivery_start, crop }) {
  // Priority 1: ship_month string like "Oct 26" / "October 2026"
  if (ship_month) {
    const m = String(ship_month).trim().match(/^([A-Za-z]+)\s+(\d{2,4})$/);
    if (m) {
      const namePrefix = m[1].slice(0, 3);
      const monthIdx = _MONTH_NAMES.findIndex(mn => mn.toLowerCase() === namePrefix.toLowerCase());
      if (monthIdx >= 0) {
        const yr2 = m[2];
        const year = yr2.length === 2 ? 2000 + parseInt(yr2, 10) : parseInt(yr2, 10);
        return _cropYearFromMonthYear(monthIdx + 1, year, crop);
      }
    }
  }
  // Priority 2: delivery_start date.
  // Parse the YYYY-MM-DD string directly — `new Date('2026-10-01')` reads
  // as UTC midnight, which in negative-offset timezones (Eastern, etc.)
  // resolves to Sep 30 of the prior month. Corn delivered Oct 1 then
  // slips below the Oct new-crop cutoff and the contract gets attributed
  // to the wrong crop year. Direct string parse avoids the timezone hop.
  if (delivery_start) {
    const m = String(delivery_start).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) {
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      if (year && month >= 1 && month <= 12) {
        return _cropYearFromMonthYear(month, year, crop);
      }
    }
  }
  // Priority 3: futures contract month code
  if (futures_symbol) {
    const sym = parseFuturesSymbol(futures_symbol);
    if (sym) {
      const monthNum = _FUTURES_CODE_TO_MONTH_NUM[sym.monthCode];
      // Use the crop derived from the futures symbol if not provided
      const cropForRule = crop || sym.crop;
      if (monthNum) return _cropYearFromMonthYear(monthNum, sym.year, cropForRule);
    }
  }
  return null;
}

// `farmerId` may be a single id OR the merged card's full id list. A farmer card
// merges same-name `farmers` rows (see normFarmerName / the 2027-rollover dupes),
// and the year-count badges + the positions overview both already read
// `allFarmerDbIds`. This used to read only the primary id, so on a merged card
// the year pill could say "2025 · 12" while the tab showed none, and the
// dashboard's % priced would disagree with the position card's. One scope for
// every grain read.
async function grainLoadAll(farmerId, cropYear) {
  const ids = [...new Set((Array.isArray(farmerId) ? farmerId : [farmerId]).filter(Boolean))];
  if (!ids.length) return { contracts: [], plan: [], costs: [], fwdCurve: [], futuresRef: [], cop: [] };
  const owner = `farmer_id=in.(${ids.join(',')})`;
  const [contracts, plan, costs, fwdCurve, futuresRef, cop] = await Promise.all([
    sbFetch('grain_contracts',     { filters: [owner, `crop_year=eq.${cropYear}`] }).catch(() => []),
    sbFetch('grain_crop_plan',     { filters: [owner, `crop_year=eq.${cropYear}`] }).catch(() => []),
    sbFetch('grain_costs',         { filters: [owner, `crop_year=eq.${cropYear}`] }).catch(() => []),
    sbFetch('grain_forward_curve', { filters: [owner, `crop_year=eq.${cropYear}`] }).catch(() => []),
    sbFetch('grain_futures_ref',   { filters: [`crop_year=eq.${cropYear}`] }).catch(() => []),
    sbFetch('grain_cop',           { filters: [owner, `crop_year=eq.${cropYear}`] }).catch(() => []),
  ]);
  return { contracts: contracts || [], plan: plan || [], costs: costs || [], fwdCurve: fwdCurve || [], futuresRef: futuresRef || [], cop: cop || [] };
}

// Map a contract form (from the modal or CSV) to the grain_contracts row shape.
// Keeps backwards compat: legacy callers passed `{ bushels, futures, type, fmonth }`
// — we still accept those names and map to the new schema columns.
function buildGrainContractPayload(farmerId, cropYear, c) {
  const contractType = c.contract_type || c.type || 'Fixed Price';
  const monthName = c.futures_month_name
    || (c.fmonth ? c.fmonth.split(' ')[0] : null);
  const monthYear = c.futures_month_year
    || (c.fmonth && c.fmonth.match(/(\d{2,4})$/) ? (() => {
        const yr = c.fmonth.match(/(\d{2,4})$/)[1];
        return yr.length === 2 ? 2000 + parseInt(yr, 10) : parseInt(yr, 10);
      })() : null);
  const futuresMonthDisplay = c.futures_month
    || (monthName && monthYear ? fmonthDisplay(monthName, monthYear) : null);
  const futuresSymbol = c.futures_symbol
    || (c.crop && monthName && monthYear ? buildFuturesSymbol(c.crop, monthName, monthYear) : null);
  const num = (v) => (v === '' || v == null ? null : Number(v));
  return {
    farmer_id: farmerId,
    crop_year: cropYear,
    contract_number: (c.contract_number || '').trim() || null,
    pricing_line_id: c.pricing_line_id || null,
    contract_type: contractType,
    crop: c.crop,
    // ⚠ WHICH PRACTICE FILLS THIS CONTRACT — and it was being DROPPED HERE.
    // GrainContractModal has rendered a Practice select since the column
    // shipped (sql/062) and puts `practice` in its onSave payload; this mapper
    // is what turns that payload into the row, and it never copied the field.
    // So the control saved nothing, an edit could not change it (a PATCH body
    // with no key leaves the column alone), and the dashboard went on reading
    // every contract as practice-less. Measured 2026-09-10, before the fix:
    // `practice` NULL on all 647 rows, 621 csv_import + 26 manual — the column
    // had never been written by any path in the app.
    //
    // Kevin, 2026-09-10: "If its organic, it should be showing in the organic
    // block on the dashboard. Allow user to select if its conventional or
    // organic when entering a contract. Also, allow someone to edit contract and
    // switch from conventional to organic or vice versa." All three are this one
    // line: the select, the edit and the organic card were already built and all
    // three were fed by a field that never left the form.
    //
    // Same class as `location`, which sat in this modal's state and its submit
    // payload with no input rendered for it, and as `adminFeeWaiver`, which was
    // parsed onto the coverage and read off a unit nothing wrote it to. A field
    // that no producer carries is a control that does nothing.
    //
    // '' means NOT STATED and must reach the row as NULL, never as a word —
    // grainContractPractice distinguishes "stated" from "derived from the
    // coverage" from "unassigned", and an empty string would break all three.
    practice: (c.practice || '').trim() || null,
    // The specialty program, when a file named one. Never derived from price.
    crop_program: c.crop_program || null,
    buyer: c.buyer || null,
    location: c.location || null,
    bushels_contracted: num(c.bushels_contracted ?? c.bushels),
    bushels_priced: num(c.bushels_priced),
    bushels_delivered: num(c.bushels_delivered),
    bushels_unpriced: num(c.bushels_unpriced),
    futures_symbol: futuresSymbol,
    futures_month: futuresMonthDisplay,
    futures_price: num(c.futures_price ?? c.futures),
    // Set only by parseGrainCSV's priced-basis branch (cash − basis). A hand-typed
    // contract always states its own futures, so it is false there by default.
    futures_derived: !!c.futures_derived,
    basis: num(c.basis),
    cash_price: num(c.cash_price),
    contract_date: c.contract_date || null,
    delivery_start: c.delivery_start || c.dstart || null,
    delivery_end: c.delivery_end || c.dend || null,
    ship_month: c.ship_month || null,
    source: c.source || 'manual',
    notes: c.notes || null,
  };
}

async function grainSaveContract(farmerId, cropYear, c) {
  const payload = buildGrainContractPayload(farmerId, cropYear, c);
  // Any hand-edit/add claims the row as 'manual' so the CSV importer leaves it
  // alone (no overwrite, no delete). A csv_import row edited here flips to manual
  // and becomes farmer-owned until explicitly re-synced from the elevator.
  payload.source = 'manual';
  // A farmer entering their own contract is the deepest engagement the portal
  // supports (a write, not a read), so it's worth its own event. No-ops when an
  // agent does the same thing in the admin app.
  trackPortalAction("save_grain_contract", { screen: "grain", crop: payload.crop || null });
  if (c.id && c.id.length > 10) {
    await fetch(`${SUPABASE_URL}/rest/v1/grain_contracts?id=eq.${c.id}`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), Prefer: 'return=minimal' },
      body: JSON.stringify(payload),
    });
    return c.id;
  }
  const rows = await sbInsert('grain_contracts', [payload]);
  return rows?.[0]?.id;
}

async function grainBulkInsertContracts(rowsForInsert) {
  if (!rowsForInsert.length) return [];
  return await sbInsert('grain_contracts', rowsForInsert);
}

// Lightweight CSV parser that handles quoted fields with commas inside.
// Returns an array of objects keyed by lowercased headers.
function parseCSVRows(text) {
  const out = [];
  const lines = text.split(/\r?\n/);
  if (!lines.length) return out;
  // Tokenize a single CSV line, respecting "double-quoted, fields"
  const tokenize = (line) => {
    const tokens = [];
    let cur = '', inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        tokens.push(cur); cur = '';
      } else { cur += ch; }
    }
    tokens.push(cur);
    return tokens.map(t => t.trim());
  };
  const headers = tokenize(lines[0]).map(h => h.toLowerCase());
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const vals = tokenize(lines[i]);
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = (vals[idx] || '').trim(); });
    out.push(obj);
  }
  return out;
}

// Parse a Schofield-style grain accounting CSV export. Each row is one
// PRICING LINE; multiple lines can share a contract_number. Returns
// rows in the shape buildGrainContractPayload expects.
//
// Recognized headers (case-insensitive):
//   contract_number, commodity, type (Fixed Price/Basis Only/Bushels Only),
//   total_contracted, total_priced, total_delivered, total_unpriced,
//   date_created, pricing_line_id, delivery_start, delivery_end,
//   location_pricing_line, futures_reference (e.g. ZCN27),
//   futures_price, basis, cash_price, quantity_priced,
//   quantity_delivered, price_comments, comments
//
// Falls back to the simpler legacy header shape (crop, buyer, bushels,
// type, futures, basis, fmonth, dstart, dend, notes) when those columns
// are present instead — keeps the old sample template working.
function parseGrainCSV(text) {
  const rows = parseCSVRows(text);
  if (!rows.length) return [];
  const sample = rows[0];
  const isSchofieldShape = 'contract_number' in sample
    && 'commodity' in sample
    && 'pricing_line_id' in sample;

  // A STRUCTURE WORD, OR NOTHING. Returns null rather than a default so the
  // caller can try the next column instead of accepting a guess.
  const structureType = (t) => {
    const s = (t || '').trim().toLowerCase();
    if (!s) return null;
    if (s.startsWith('fixed')) return 'Fixed Price';
    if (s.startsWith('basis')) return 'Basis';
    if (s.includes('hta'))     return 'HTA';
    if (s.startsWith('bushels')) return 'Bushels Only';
    return null;
  };
  // WHICH COLUMN HOLDS THE STRUCTURE IS NOT THE SAME ON EVERY EXPORT.
  // Howlett writes it in `Type`. Edgewood writes the elevator's own ledger
  // direction there — `Type` is "Purchase" on every single row, buy-side — and
  // puts the trade structure in `Subtype` ("Purchase Contract" / "Basis").
  // Reading `Type` alone therefore stamped 'Fixed Price' on all three of
  // Edgewood's basis contracts (170,000 bu of corn and wheat whose futures leg
  // is open), which values open bushels at a price nobody has locked.
  //
  // Whichever column NAMES a structure wins; a column that names none is passed
  // over rather than defaulted, so Howlett's files resolve exactly as before.
  const mapType = (type, subtype) =>
    structureType(subtype) || structureType(type) || 'Fixed Price';
  const mapCrop = (c) => grainCropFromCommodity(c) || '';
  const num = (v) => {
    if (v === '' || v == null) return null;
    const n = parseFloat(String(v).replace(/[$,]/g, ''));
    return isNaN(n) ? null : n;
  };
  const parseDate = (s) => {
    if (!s) return null;
    // Accept MM/DD/YYYY or YYYY-MM-DD
    const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m1) { const [, mo, da, yr] = m1; return `${yr}-${mo.padStart(2,'0')}-${da.padStart(2,'0')}`; }
    const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
    return null;
  };

  if (!isSchofieldShape) {
    // Legacy template shape (one row = one contract)
    return rows
      .filter(r => r.crop && r.bushels)
      .map(r => ({
        contract_number: r.contract_number || null,
        crop: r.crop,
        contract_type: mapType(r.type, r.subtype) || 'HTA',
        type: mapType(r.type, r.subtype) || 'HTA',
        buyer: r.buyer || null,
        bushels_contracted: num(r.bushels),
        futures_price: num(r.futures),
        basis: num(r.basis),
        futures_month: r.fmonth || null,
        delivery_start: parseDate(r.dstart),
        delivery_end: parseDate(r.dend),
        notes: r.notes || null,
      }));
  }

  // ── ROLLS: THE SAME BUSHELS APPEAR TWICE IN ONE FILE ────────────────────
  // Edgewood prices a basis contract by ROLLING units out of it into a lettered
  // child contract, and the export carries BOTH sides. Corn 00110159 (100,000 bu,
  // Basis) names four rolls in its own Comments and all four children —
  // 0110159A/B/C/D, 20k+20k+20k+40k — are rows in the same file. Imported as
  // read, that farm's corn book totalled 300,000 bu against the elevator's
  // 150,000; wheat totalled 97,037 against 77,037. A doubled forward-sell
  // position is the one error that reads as permission to sell nothing.
  //
  // The roll amount is SOURCED — the file states it in words: "On 02/24/2026
  // rolled 20000.00 units to Location: 1 Contract: 0110159A." Nothing else in
  // the export nets it out: Total_Unpriced on the parent still reads the FULL
  // 100,000 after every unit has left, so the arithmetic columns cannot answer
  // this and only the sentence can.
  //
  // ⚠ THE CHILDREN REPEAT THE PARENT'S ROLL HISTORY, AND SUBTRACTING ON EVERY
  // ROW THAT MENTIONS ONE IS A DISASTER. 0110159D is a 40,000 bu child whose own
  // comment names the three rolls that preceded it (A, B, C = 60,000) — netting
  // those out of D would drive it to −20,000. The discriminator is the file's
  // own graph, not the wording: A ROW THAT IS ITSELF A ROLL TARGET IS A CHILD,
  // and a child never nets. Only a row nothing rolled INTO can roll bushels out.
  //
  // The second guard is the direction that costs money. A roll is netted ONLY
  // when the child it names is present in THIS file — an export filtered by
  // commodity or date can hold a parent whose children are elsewhere, and
  // subtracting there would delete bushels the farm still owes. Unresolved rolls
  // are counted and reported, never applied.
  const rollsOf = (r) => {
    const out = [];
    const text = [r.comments, r.price_comments].filter(Boolean).join(' ');
    const re = /rolled\s+([\d,]+(?:\.\d+)?)\s+units?\s+to\b[\s\S]{0,60}?Contract:\s*([A-Za-z0-9._/-]+)/gi;
    let m;
    while ((m = re.exec(text)) !== null) {
      const qty = parseFloat(m[1].replace(/,/g, ''));
      const target = String(m[2]).replace(/[.,;]+$/, '').trim();
      if (isFinite(qty) && qty > 0 && target) out.push({ qty, target });
    }
    return out;
  };
  const schofieldRows = rows.filter(r => r.contract_number && r.commodity);
  const numbersInFile = new Set(schofieldRows.map(r => String(r.contract_number).trim()));
  const rollTargetsInFile = new Set();
  for (const r of schofieldRows) for (const roll of rollsOf(r)) rollTargetsInFile.add(roll.target);

  // Schofield shape: one CSV row per pricing line. Use the row directly —
  // each pricing line becomes one grain_contracts row. The contract_number
  // groups them in the UI. crop_year is derived per-row from delivery date
  // or futures symbol (NOT the dropdown selection — these contracts can
  // span multiple crop years).
  return schofieldRows
    .map(r => {
      const sym = parseFuturesSymbol(r.futures_reference);
      const monthName = sym?.monthName || null;
      const monthYear = sym?.year || null;
      const quantityPriced = num(r.quantity_priced);
      const quantityDelivered = num(r.quantity_delivered);
      const totalContracted = num(r.total_contracted);
      const totalPriced = num(r.total_priced);
      const totalDelivered = num(r.total_delivered);
      const totalUnpriced = num(r.total_unpriced);
      // One row per pricing line. Each line carries the bushels PRICED on it; the
      // header line (P00) additionally carries the contract's UNPRICED remainder.
      // This makes Σ(rows) = Total_Contracted for BOTH single-line and fully-split
      // contracts. The old "header = Total_Contracted" rule double-counted a fully
      // split contract (e.g. 103451: P00 took the 15k total on top of P01+P02's 5k
      // each → 25k instead of 15k).
      // The header suffix is `-P00` on Howlett's export and a bare `-00` on
      // Edgewood's. Both are line zero of the contract; only the spelling
      // differs. Left at `-P00`, an Edgewood basis parent matched nothing, so
      // its unpriced remainder was dropped from the row's bushels entirely.
      const isHeaderLine = (r.pricing_line_id || '').match(/-P?0+$/);
      const unpriced = totalUnpriced != null ? totalUnpriced : Math.max(0, (totalContracted || 0) - (totalPriced || 0));
      const crop = mapCrop(r.commodity);
      const cropProgram = grainProgramFromCommodity(r.commodity);
      const deliveryStart = parseDate(r.delivery_start);

      // HTA detection: the Schofield-style CSV labels futures-locked /
      // basis-pending contracts as "Fixed Price" because the type field
      // is the elevator's billing category, not the trade structure.
      // The signal is futures>0, basis=0, cash_price=0/null. Reclassify
      // those as HTA (Hedge to Arrive) so the dashboard shows the right
      // type pill and the avg-cash logic uses futures-only valuation.
      let detectedType = mapType(r.type, r.subtype);
      const _fp = num(r.futures_price);
      const _bs = num(r.basis);
      const _cp = num(r.cash_price);
      // A LINE WITH NO PRICE ON IT PRICED NOTHING, WHATEVER `Quantity_Priced`
      // SAYS. Edgewood's basis parents state `Total_Priced 0.000000` and
      // `Total_Unpriced 100000` and then carry `Quantity_Priced 100000` on the
      // same row — the column is the quantity ON the line, not the quantity
      // priced, and the two only diverge here. Trusting it reported 170,000 bu
      // of open basis bushels as priced across corn and wheat.
      //
      // The test is structural rather than a cross-column tie-out, so it holds
      // on a multi-line contract too: a line stating neither a futures price nor
      // a positive cash price has no price to have priced at. Measured against
      // the live book before shipping — 0 of 627 existing rows are in this
      // shape, so this moves nothing that is already imported, and it leaves
      // W Cleveland's 104964-P00 (priced 0 already) exactly as it was.
      const lineStatesAPrice = (_fp != null && _fp !== 0) || (_cp != null && _cp > 0);
      const pricedOnLine = lineStatesAPrice ? (quantityPriced || 0) : 0;
      const quantityPricedIgnored = !lineStatesAPrice && (quantityPriced || 0) > 0
        ? quantityPriced : 0;
      const bushelsForRow = pricedOnLine + (isHeaderLine ? unpriced : 0);
      if (detectedType === 'Fixed Price'
          && _fp != null && _fp > 0
          && (_bs == null || _bs === 0)
          && (_cp == null || _cp === 0)) {
        detectedType = 'HTA';
      }
      // A BASIS CONTRACT THAT HAS BEEN PRICED IS NOT A BASIS CONTRACT ANY MORE
      // (2026-08-20). Howlett's export writes basis and CASH on a priced pricing
      // line and leaves the futures column EMPTY — the elevator's `type` is the
      // structure the contract was WRITTEN at, not what it is now. Left as
      // 'Basis', netPrice() valued it at TODAY'S board plus the basis, so 49
      // lines / 278,413 bu of already-locked grain floated with the market:
      // Jeffres 103585 read mkt $4.98 + 0.28 = $5.26 against a locked $5.18.
      //
      // THE PRICED SIGNAL IS `quantity_priced > 0`, NOT the presence of a cash
      // price. The P00 header row carries the contract's UNPRICED remainder and
      // still shows the contract's cash — W Cleveland 104964-P00 is exactly that
      // (priced 0, unpriced 113.21, cash 5.29) and must STAY a basis contract,
      // floating, because those bushels genuinely have no price yet.
      //
      // Futures is then `cash − basis`, which is the file's OWN arithmetic:
      // measured across every row carrying all three, it reconciles to the
      // half-cent on 494 of 502 (the 8 that don't are split contracts where
      // `basis` is contract-level while `cash` is per line). Deriving FROM cash
      // rather than trusting a futures leg and re-adding basis is what keeps
      // those exact — cash is the number the elevator actually priced at.
      let derivedFutures = null;
      if (detectedType === 'Basis'
          && _fp == null
          && _bs != null
          && _cp != null && _cp > 0
          && pricedOnLine > 0) {
        derivedFutures = +(_cp - _bs).toFixed(4);
        detectedType = 'Fixed Price';
      }

      // Net out the bushels this contract has already rolled into a child that
      // is ALSO in this file. See the block above rollsOf() for both guards —
      // a row that is itself a roll target never nets, and a roll whose child is
      // absent is reported rather than applied.
      const myNumber = String(r.contract_number).trim();
      const isRollTarget = rollTargetsInFile.has(myNumber);
      let rolledOutBu = 0, rollTargets = [], rollsUnresolved = [];
      if (!isRollTarget) {
        for (const roll of rollsOf(r)) {
          if (roll.target === myNumber) continue;              // can't roll into itself
          if (numbersInFile.has(roll.target)) { rolledOutBu += roll.qty; rollTargets.push(roll.target); }
          else rollsUnresolved.push(roll);
        }
      }
      // Refuse rather than store a negative. A roll that claims more than the
      // contract holds means the sentence was misread, and a negative bushel
      // count would quietly subtract from the farm's whole position.
      const rollOverruns = rolledOutBu > bushelsForRow + 1e-6;
      if (rollOverruns) { rolledOutBu = 0; rollTargets = []; }
      const bushelsNet = Math.max(0, bushelsForRow - rolledOutBu);

      const cropYear = deriveContractCropYear({
        ship_month: null,                     // Schofield CSV has no ship_month column
        futures_symbol: sym?.symbol,
        delivery_start: deliveryStart,
        crop,
      });
      return {
        contract_number: r.contract_number,
        pricing_line_id: r.pricing_line_id || null,
        crop,
        crop_program: cropProgram,
        crop_year: cropYear,
        contract_type: detectedType,
        type: detectedType,    // legacy compat
        // THE BUYER IS NOT `account` — `account` IS THE SELLER (fixed 2026-08-20).
        // In the Merchant Ag / Schofield export, `account` is the PRODUCER's account
        // at the elevator, so mapping it to `buyer` wrote the grower's own name into
        // the counterparty column on every imported contract: measured book-wide,
        // all 473 csv_import rows carried the farmer's own name, across all 9 farms,
        // and those exact strings ("Meisenzahl Farms LLC", "Silsby Settlement Farm",
        // "McIlroy Farms, LLC") are Howlett's own customer roster in `howlett_accounts`.
        // It is not a cosmetic label: Meisenzahl's Howlett contracts 103961/104502
        // read as "Meisenzahl Farms LLC" contracts, so nobody could find them when
        // looking for the Howlett ones, and both got hand-entered a second time —
        // 7,000 bu of soybeans double-counted against the forward-sell guarantee.
        // The counterparty is a property of the FILE, not a column in it, so it now
        // comes from the import panel (see grainBuyerFromRows) and is never guessed
        // from the account name.
        buyer: r.buyer || null,
        account: r.account || null,   // the elevator's name FOR THIS FARM; preview only, never stored
        location: r.location_pricing_line || r.location_contracted || null,
        bushels_contracted: bushelsNet,
        bushels_priced: pricedOnLine,              // per pricing line (Σ = Total_Priced)
        bushels_delivered: quantityDelivered,
        // The remainder still OPEN on this contract. Rolled-out units have gone
        // to a child contract that carries them in its own right, so they are no
        // longer unpriced here either — netting the bushels and not this would
        // leave a fully-rolled parent reading 0 bu contracted with 100,000 bu
        // unpriced against it.
        bushels_unpriced: isHeaderLine ? Math.max(0, unpriced - rolledOutBu) : null,
        futures_symbol: sym?.symbol || null,
        futures_month: sym?.display || null,
        futures_month_name: monthName,
        futures_month_year: monthYear,
        futures_price: derivedFutures != null ? derivedFutures : num(r.futures_price),
        // OURS, not the file's — see the priced-basis block above. Labelled in
        // the contracts table so a derived leg never reads as a stated one.
        futures_derived: derivedFutures != null,
        basis: num(r.basis),
        cash_price: num(r.cash_price),
        contract_date: parseDate(r.date_created),
        delivery_start: deliveryStart,
        delivery_end: parseDate(r.delivery_end),
        notes: [r.comments, r.price_comments].filter(Boolean).join(' | ') || null,
        // Preview-only, never stored — buildGrainContractPayload picks named
        // columns, so these ride along to the import panel and stop there.
        // They exist so a bushel this parser REMOVED is on screen next to the
        // row it came off, rather than being a number that quietly got smaller.
        _rolledOutBu: rolledOutBu || 0,
        _rollTargets: rollTargets,
        _rollsUnresolved: rollsUnresolved,
        _rollOverran: rollOverruns,
        _quantityPricedIgnored: quantityPricedIgnored,
        _fileContracted: totalContracted,
      };
    });
}

// Who BOUGHT the grain in a contracts export. The file does not carry a buyer
// column — one export is one elevator — so this only reports what the file
// STATES and returns null otherwise, leaving the import panel to ask. It never
// reads `account`, which is the seller (see parseGrainCSV).
//
// The location column is the one place the elevator names itself: Howlett writes
// "Howlett Lakeville" / "Howlett Batavia" / "Howlett LeRoy", the sample export
// writes "Schofield Farms". A bare site or term — "Avon", "Picked Up", "Unknown"
// — names a place, not a company, and resolves to nothing on purpose: Avon has
// had more than one grain buyer in it, and inferring one from a town is exactly
// the kind of story-that-fits-the-data this repo keeps paying for.
const GRAIN_BUYER_HINT = /(farms?|grain|elevator|co-?op|milling|ag\b)/i;
function grainFileLocations(rows) {
  return [...new Set((rows || []).map(r => String(r.location || '').trim()).filter(Boolean))];
}
function grainBuyerFromRows(rows) {
  const locs = grainFileLocations(rows);
  if (locs.some(l => /^howlett\b/i.test(l))) return 'Howlett Farms';
  const named = [...new Set(locs.filter(l => GRAIN_BUYER_HINT.test(l)))];
  return named.length === 1 ? named[0] : null;
}

// ⚠ WHEN THE FILE DOESN'T NAME ITS BUYER, THE BOOK'S ANSWER IS A GUESS — AND A
// SECOND ELEVATOR'S FILE INHERITED THE FIRST ELEVATOR'S NAME (2026-09-01).
//
// Where grainBuyerFromRows finds nothing, the panel pre-fills from this farm's
// existing imported contracts when they all agree. That is a good default for
// the second Howlett upload and a wrong one for the first Edgewood upload, and
// it is not hypothetical: Kevin imported Edgewood's wheat export for R L Jeffres
// on 2026-09-01, the pre-fill offered "Howlett Farms" because every csv_import
// row he had said so, and six Edgewood contracts (97,037 bu) landed under
// Howlett's name. The soft "confirm it" label was on screen and got clicked past
// — which is what a soft label does.
//
// The file was not silent about this. Every one of those rows stored
// `location = "EDGEWOOD"`, in a book whose Howlett rows read "Howlett Lakeville"
// / "Howlett Batavia" / "Howlett LeRoy". So the contradiction was recorded on
// the row itself and nothing looked at it.
//
// This looks for exactly that CONTRADICTION and nothing more. A candidate fits
// unless the file's own locations rule it out: strip the generic words that any
// elevator's name contains ("farms", "grain", "inc") and require at least one
// distinctive token to appear somewhere in the file's locations. "Howlett Farms"
// keeps "howlett", which is in every Howlett location and in none of Edgewood's.
//
// It DETECTS DISAGREEMENT, it does not prove agreement — so a file with no
// location column, or a candidate that is nothing but generic words, fits by
// default rather than being refused on no evidence. And it is only ever applied
// to the BOOK guess: a buyer the file itself names, or one a human typed, is
// never second-guessed by it.
const GRAIN_BUYER_GENERIC = /^(farms?|grain|elevator|co|coop|inc|llc|ltd|company|ag|and|of|the|sons|son|feed|mill|milling|energy)$/i;
function grainBookBuyerFits(rows, candidate) {
  const locs = grainFileLocations(rows).map(l => l.toLowerCase());
  if (!locs.length) return true;                       // nothing in the file to contradict it
  const tokens = String(candidate || '').toLowerCase().split(/[^a-z0-9]+/)
    .filter(t => t && !GRAIN_BUYER_GENERIC.test(t));
  if (!tokens.length) return true;                     // no distinctive token to test with
  return tokens.some(t => locs.some(l => l.includes(t)));
}

// WHICH IMPORTED ROWS THIS UPLOAD MAY REMOVE.
//
// An export is the source of truth for its OWN ELEVATOR, and until 2026-09-01
// the sweep did not know elevators existed. It removed any `csv_import` row
// absent from the upload whose (crop, crop_year) the upload covered — which is
// exactly right while a farm sells to one buyer, and destroys the other book
// the moment it sells to two.
//
// R L Jeffres sells to both. Measured on the live book: his CY2026 holds 8
// Howlett corn contracts (140,000 bu) and 1 Howlett wheat contract (10,000 bu),
// and Edgewood's corn and wheat exports cover exactly those two crop-years.
// Importing them would have DELETED all 150,000 bu. The existing "removes more
// than it keeps" banner would have caught the corn (8 stale vs 8 incoming) and
// stayed silent on the wheat (1 vs 6) — so half of it went unwarned.
//
// A row is now removable only when the buyer on it MATCHES the buyer being
// imported. Everything else in scope is kept and REPORTED:
//   • another elevator's contracts are not this file's to retire;
//   • a row with NO buyer recorded (17 in the book, written before the column
//     existed) cannot be attributed to anyone, and an unattributable row is
//     kept. A leftover duplicate is visible on screen; a deleted live contract
//     is silent, and silence is the failure this whole importer keeps paying
//     for.
// One rule, two callers — the preview count and the delete both run it, so the
// number on the button is the number of rows that go.
function grainImportStaleIds(existingRows, parsedRows, buyer) {
  const key = (r) => `${r.contract_number || ''}|${r.pricing_line_id || ''}`;
  const incomingKeys = new Set((parsedRows || []).map(key));
  const fileScopes = new Set((parsedRows || []).map(r => `${r.crop}|${r.crop_year}`));
  const want = String(buyer || '').trim().toLowerCase();
  const staleIds = [], keptOtherBuyer = [];
  for (const r of existingRows || []) {
    if (r.source !== 'csv_import') continue;
    if (incomingKeys.has(key(r))) continue;                    // still in the file
    if (!fileScopes.has(`${r.crop}|${r.crop_year}`)) continue; // different crop/year than this upload
    if (!want || String(r.buyer || '').trim().toLowerCase() !== want) { keptOtherBuyer.push(r); continue; }
    staleIds.push(r.id);
  }
  return { staleIds, keptOtherBuyer };
}

async function grainDeleteContract(id) {
  await sbDelete('grain_contracts', `id=eq.${id}`);
}

async function grainSavePlan(farmerId, cropYear, crop, fields) {
  const payload = { farmer_id: farmerId, crop_year: cropYear, crop };
  // Selectively include only the fields the caller passed so we don't
  // wipe sibling values on partial updates.
  for (const key of ['acres', 'yield_per_acre', 'aph_yield', 'coverage_level', 'insurance_guarantee_override', 'storage_mode', 'actual_production', 'chopped_acres']) {
    if (fields[key] !== undefined) payload[key] = fields[key];
  }
  await sbUpsert('grain_crop_plan', [payload], 'farmer_id,crop_year,crop');
}

async function grainSaveCost(farmerId, cropYear, crop, costPerAcre) {
  await sbUpsert('grain_costs', [{ farmer_id: farmerId, crop_year: cropYear, crop, cost_per_acre: costPerAcre }], 'farmer_id,crop_year,crop');
}

async function grainSaveCOP(farmerId, cropYear, crop, mode, lineItems, totalPerAcre) {
  await sbUpsert('grain_cop', [{
    farmer_id: farmerId, crop_year: cropYear, crop, mode,
    line_items: lineItems, total_per_acre: totalPerAcre, updated_at: new Date().toISOString(),
  }], 'farmer_id,crop_year,crop');
}

async function grainSaveFwdCurve(farmerId, cropYear, crop, deliveryMonth, pct, basis, futuresPrice) {
  await sbUpsert('grain_forward_curve', [{
    farmer_id: farmerId, crop_year: cropYear, crop,
    delivery_month: deliveryMonth, pct_allocation: pct,
    basis, futures_price: futuresPrice,
  }], 'farmer_id,crop_year,crop,delivery_month');
}

async function grainSaveFuturesRef(cropYear, crop, price, refMonth) {
  await sbUpsert('grain_futures_ref', [{ crop_year: cropYear, crop, price, ref_month: refMonth }], 'crop_year,crop');
}

// WHOLE BUSHELS. Scale tickets settle to three decimals and the contract sums carry
// them through, so this was printing "34,203.639 bu" and "2,019.881 bu" — precision
// that is real in the file and meaningless on a plan nobody markets a fraction of a
// bushel. DISPLAY only: every figure behind it stays exact, because rounding a yield
// or a guarantee into the arithmetic is its own bug (see grainBushelsFromCoverage).
const gFmtBu  = n => n == null ? '—' : Math.round(Number(n)).toLocaleString('en-US') + ' bu';
// Acres to 2dp — the precision RMA itself reports them at — which also kills the
// float artifact a sum of coverage lines leaves behind (367.1 + 115.61 came out
// 482.71000000000004 and went straight into an input box).
const gFmtAc  = n => n == null || n === '' ? '' : (Math.round(Number(n) * 100) / 100).toLocaleString('en-US');
// Yields to 1dp. The acre-weighted APH is a derived average, so its 14-digit tail
// (75.04199208634584) is arithmetic residue, not accuracy.
const gFmtY   = n => n == null || n === '' ? '' : String(Math.round(Number(n) * 10) / 10);
const gFmtD   = n => n == null ? '—' : '$' + Number(n).toFixed(2);
const gFmtPct = n => Math.round(n) + '%';
const gFmtBig = n => {
  const abs = Math.abs(n), neg = n < 0;
  if (abs >= 1e6) return (neg ? '-' : '') + '$' + (abs / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return (neg ? '-' : '') + '$' + (abs / 1e3).toFixed(1) + 'K';
  return (neg ? '-$' : '$') + Math.abs(Math.round(n)).toLocaleString();
};
const gpColor = n => n > 0 ? '#2d6a1a' : n < 0 ? '#c0392b' : '#8a6a00';

// ───────────────────────────────────────────────────────────────────────────
// MARKET INTELLIGENCE  (Phase 1 — descriptive basis analytics, no forecasting)
// Backend: api/cash-bids.js, api/basis-history.js, api/corn-basis.js,
//          api/weather-sync.js  +  tables cash_bids_daily / futures_settles_daily
//          / basis_calculated_daily / weather_county_daily.
// Sources: 'howlett' (manual entry), 'wny_energy' (auto-scraped corn-basis.js).
// ───────────────────────────────────────────────────────────────────────────
const MI_GREEN = "#1a2e1a", MI_GREEN2 = "#2d4a2d", MI_BG = "#f2f6f2", MI_MUTE = "#60735f";
const MI_SOURCES = { howlett: "Howlett Farms", wny_energy: "WNY Energy", star_of_west: "Star of the West", adm_buffalo: "ADM Buffalo" };
// Auto-scraped elevator cash-bid boards surfaced on the Basis tab, organized as
// elevator → location → commodity. Each location is its own cash_bids_daily
// `source`. Single-file scrapers (corn/sotw/adm) plus the generalized
// _basis-scrape.js (multi-location elevators like Deerfield) all write here.
// Adding a location = one entry; the Basis tab drills elevator → location →
// commodity, driven by what data is actually present.
const MI_ELEVATORS = [
  { id: "wny_energy", label: "WNY Energy", region: "NY", locations: [{ source: "wny_energy", label: "Medina, NY" }] },
  { id: "star_of_west", label: "Star of the West", region: "NY", locations: [{ source: "star_of_west", label: "Churchville, NY" }] },
  { id: "adm_buffalo", label: "ADM Buffalo", region: "NY", locations: [{ source: "adm_buffalo", label: "Buffalo, NY" }] },
  { id: "deerfield", label: "Deerfield Ag Services", region: "OH", locations: [
    { source: "deerfield_deerfield", label: "Deerfield, OH" },
    { source: "deerfield_louisville", label: "Louisville, OH" },
    { source: "deerfield_kinsman", label: "Kinsman, OH" },
    { source: "deerfield_massillon", label: "Massillon, OH" },
  ] },
];
const MI_COMMODITY_LABELS = { corn: "Corn", soy: "Soybeans", wheat: "Wheat", plenish: "Plenish (HO soy)", oats: "Oats", barley: "Barley", milo: "Milo" };
const miCommodityLabel = (c) => MI_COMMODITY_LABELS[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : c);
const miSigned = (v, d = 2) =>
  v == null || isNaN(v) ? "—" : (v > 0 ? "+" : "") + Number(v).toFixed(d);
const miDeltaColor = (v) => (v > 0 ? "#2d6a1a" : v < 0 ? "#c0392b" : MI_MUTE);

function MILineChart({ series, d3Lib, height = 280, color = "#2d6a1a", isMobile }) {
  // series: [{ date: 'YYYY-MM-DD', value: number }] already sorted ascending.
  const W = isMobile ? 340 : 760, H = height, M = { t: 16, r: 16, b: 28, l: 44 };
  const pts = (series || []).filter((p) => p.value != null && !isNaN(p.value));
  if (!d3Lib || pts.length === 0) {
    return (
      <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", color: MI_MUTE, fontSize: 13 }}>
        {d3Lib ? "No basis data for this selection yet." : "Loading chart…"}
      </div>
    );
  }
  // Parse date-only strings ("YYYY-MM-DD") as LOCAL calendar dates. new Date(str)
  // treats them as UTC midnight, which d3's local-time formatter/intervals then
  // shift back a day (May 28 → "May 27", and a 1-day span collapses to a single
  // local-midnight tick). Parsing local keeps the day correct.
  const parseDay = (s) => {
    if (typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [yy, mm, dd] = s.split("-").map(Number);
      return new Date(yy, mm - 1, dd);
    }
    return new Date(s);
  };
  const x = d3Lib.scaleTime()
    .domain(d3Lib.extent(pts, (p) => parseDay(p.date)))
    .range([M.l, W - M.r]);
  const yExt = d3Lib.extent(pts, (p) => p.value);
  const pad = (yExt[1] - yExt[0]) * 0.15 || 0.1;
  const y = d3Lib.scaleLinear()
    .domain([yExt[0] - pad, yExt[1] + pad])
    .range([H - M.b, M.t]);
  const line = d3Lib.line().x((p) => x(parseDay(p.date))).y((p) => y(p.value));
  const yTicks = y.ticks(5);
  // X ticks: with only a handful of scrape days, tick on the ACTUAL data dates so
  // every day shows (count/interval ticks can collapse a 1–2 day span to one
  // tick). Wider spans use count-based ticks; >3 months switches day → month/year.
  const [d0, d1] = x.domain();
  const spanDays = (d1 - d0) / 86400000;
  const xFmt = d3Lib.timeFormat(spanDays > 92 ? "%b '%y" : "%b %d");
  const maxTicks = isMobile ? 4 : 7;
  const dayTimes = [...new Set(pts.map((p) => +parseDay(p.date)))].sort((a, b) => a - b);
  let xTicks;
  if (dayTimes.length <= maxTicks) {
    xTicks = dayTimes.map((t) => new Date(t));
  } else if (spanDays <= 14) {
    xTicks = x.ticks(d3Lib.timeDay.every(Math.max(1, Math.ceil(spanDays / maxTicks))));
  } else {
    xTicks = x.ticks(maxTicks);
  }
  const seenTick = new Set();
  xTicks = xTicks.filter((t) => { const k = xFmt(t); if (seenTick.has(k)) return false; seenTick.add(k); return true; });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H }}>
      {yTicks.map((t, i) => (
        <g key={i}>
          <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} stroke="#e3ece3" />
          <text x={M.l - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill={MI_MUTE}>
            {t.toFixed(2)}
          </text>
        </g>
      ))}
      {xTicks.map((t, i) => (
        <text key={i} x={x(t)} y={H - 8} textAnchor="middle" fontSize="10" fill={MI_MUTE}>
          {xFmt(t)}
        </text>
      ))}
      <line x1={M.l} x2={W - M.r} y1={y(0)} y2={y(0)} stroke={MI_MUTE} strokeDasharray="3 3" opacity={yExt[0] < 0 && yExt[1] > 0 ? 0.7 : 0} />
      <path d={line(pts)} fill="none" stroke={color} strokeWidth="2" />
      {pts.length < 60 && pts.map((p, i) => (
        <circle key={i} cx={x(parseDay(p.date))} cy={y(p.value)} r="2.5" fill={color} />
      ))}
    </svg>
  );
}

function MarketIntelligenceView({ isMobile = false, canViewElevator = true, mode = null }) {
  // The Yield tab (WNY county corn-yield model) is DELIBERATELY on the portal
  // side. Market Intelligence has a second audience — a partner grain elevator
  // (Howlett Farms) — and the yield prediction is the part of it Clover Ag
  // provides them; the county catchment / draw-area selector was built for their
  // draw area. It was briefly gated to agents on 2026-08-05 (b0b434b) on the
  // theory that its weight had killed Christian Cleveland's tab; that removed the
  // one feature the elevator is here for, and Kevin confirmed the tab had been
  // working. Reverted same day. If page weight needs addressing, make the mount
  // cheaper — do not take the tab away.
  //
  // Its survey inputs (planting_survey_farmers / _intentions / _progress) are
  // admin-only by policy, so a portal viewer gets zero rows there and the model
  // falls back to the NASS pace. That fallback is by design, not a defect.
  //
  // The year of cash bids is read only when the Basis tab opens: it is the one
  // tab that uses it. The hidden Dashboard / History / Bid entry tabs, and the
  // year of basis and futures rows only the Dashboard drew, were cut 2026-11-02.
  // The second Targets tab went too: Grain Marketing's Targets is the one copy.
  const [tab, setTab] = useState("yield");
  const [bidsState, setBidsState] = useState("idle");   // idle | loading | ready
  const [d3Lib, setD3Lib] = useState(window.d3 || null);
  const [bids, setBids] = useState([]);

  // Unmount guard only. Leaving the Basis tab mid-read must NOT drop the result,
  // or coming back would sit on "Loading" for good.
  const miMounted = useRef(true);
  useEffect(() => () => { miMounted.current = false; }, []);
  useEffect(() => {
    if (tab !== "basis" || bidsState !== "idle") return;
    setBidsState("loading");
    const since365 = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
    Promise.all([
      d3Promise,
      sbFetch("cash_bids_daily", { filters: [`bid_date=gte.${since365}`], order: "bid_date.desc" }).catch(() => []),
    ]).then(([d3Loaded, b]) => {
      if (!miMounted.current) return;
      if (d3Loaded) setD3Lib(d3Loaded);
      setBids(b || []);
      setBidsState("ready");
    }).catch(() => { if (miMounted.current) setBidsState("ready"); });
  }, [tab, bidsState]);

  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} style={{
      padding: "8px 16px", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700,
      background: tab === key ? MI_GREEN : "#e3ece3", color: tab === key ? "#fff" : MI_GREEN2,
    }}>{label}</button>
  );

  // No in-page logo and no extra padding: the app shell already carries the
  // brand and the page gutter, and this page was the one that doubled both.
  return (
    <div style={{ background: MI_BG, minHeight: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <h2 style={{ ...uiPageTitle(isMobile) }}>Market Intelligence</h2>
          <div style={{ fontSize: 12.5, color: MI_MUTE, marginTop: 3 }}>County corn yield &amp; production · Western New York</div>
          {tab === "basis" && bids.length > 0 && (
            <FreshAsOf date={latestTs(bids, "bid_date")} prefix="Cash bids through"
              style={{ marginTop: 5, display: "inline-block" }} />
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {tabBtn("yield", "🌽 Yield")}
          {tabBtn("basis", "📉 Basis")}
          {canViewElevator && tabBtn("elevator", "🏢 Elevator")}
          {tabBtn("brief", "📧 Brief")}
        </div>
      </div>

      {tab === "elevator" && canViewElevator && <ElevatorPanel isMobile={isMobile} />}

      {tab === "basis" && bidsState !== "ready" && <div style={{ color: MI_MUTE, padding: 24 }}>Loading market data…</div>}
      {tab === "basis" && bidsState === "ready" && (
        <MIBasisTab bids={bids} d3Lib={d3Lib} isMobile={isMobile} />
      )}

      {tab === "brief" && <MIBriefTab isMobile={isMobile} />}

      {/* isPortal gates the RAW data path, which reads the planting-survey views.
          See the H-1 note on MIYieldView — this is a security boundary. */}
      {tab === "yield" && <MIYieldView isPortal={mode === "portal"} />}
    </div>
  );
}

// ── BASIS TAB ────────────────────────────────────────────────────────────────
// WNY Energy (Medina, NY) posted corn bids — current board + historical basis
// chart + alert-recipient manager. Reads cash_bids_daily.published_basis (the
// elevator's authoritative posted basis, written by api/corn-basis.js). The
// daily scrape emails recipients here when any month's basis moves ≥ $0.10/bu.
const MI_MON_IDX = { JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5, JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };
function miPeriodSortKey(period) {
  const m = String(period || "").toUpperCase().match(/^([A-Z]{3})(\d{2})$/);
  if (!m || MI_MON_IDX[m[1]] == null) return Number.MAX_SAFE_INTEGER;
  return (2000 + parseInt(m[2], 10)) * 12 + MI_MON_IDX[m[1]];
}

// Shared recipient-list manager for the MI email features (basis alerts +
// daily brief) — both back onto identical {id,email,active} tables.
// `regions` is optional: pass the MI_REGIONS entries and each recipient gets to
// pick which region's brief they receive (mi_brief_recipients.region, sql/036).
// Omit it and the manager behaves exactly as before — the basis-change alert list
// has no region and must not grow a column it doesn't have.
function MIRecipientManager({ table, title, blurb, regions }) {
  const regionKeys = useMemo(() => (regions || []).map((r) => r.key), [regions]);
  const [recips, setRecips] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newRegion, setNewRegion] = useState(regionKeys[0] || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    sbFetch(table, { order: "created_at.asc" }).then((r) => setRecips(r || [])).catch(() => setRecips([]));
  }, [table]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    const email = newEmail.trim().toLowerCase(); setErr("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr("Enter a valid email address."); return; }
    setBusy(true);
    try { await sbInsert(table, [{ email, active: true, ...(regions ? { region: newRegion } : {}) }]); setNewEmail(""); load(); }
    catch (e) { setErr("Could not add — that email may already be on the list."); }
    finally { setBusy(false); }
  };
  // Upsert on the primary key rather than a PATCH helper (there isn't one). The
  // whole stored row goes back with only `region` changed, so nothing else moves.
  const changeRegion = async (row, region) => {
    if (!regions || region === row.region) return;
    setErr(""); setBusy(true);
    setRecips((prev) => prev.map((r) => (r.id === row.id ? { ...r, region } : r)));   // optimistic
    try { await sbUpsert(table, [{ ...row, region }], "id"); load(); }
    catch (e) { setErr("Could not change region — " + e.message); load(); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true);
    try { await sbDelete(table, `id=eq.${id}`); load(); }
    catch (e) { setErr("Could not remove recipient."); }
    finally { setBusy(false); }
  };
  const cardS = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 10, padding: 16, marginBottom: 16 };
  return (
    <div style={cardS}>
      <strong style={{ color: MI_GREEN2, fontSize: 15 }}>{title}</strong>
      {blurb && <div style={{ fontSize: 13, color: MI_MUTE, margin: "4px 0 12px" }}>{blurb}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "10px 0" }}>
        <input type="email" value={newEmail} placeholder="name@example.com"
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #ccd", minWidth: 220, fontSize: 14 }} />
        {regions && (
          <select value={newRegion || ""} onChange={(e) => setNewRegion(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #ccd", fontSize: 14, background: "#fff", color: MI_GREEN }}>
            {regions.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        )}
        <button onClick={add} disabled={busy} style={{ padding: "8px 16px", border: "none", borderRadius: 8, cursor: busy ? "default" : "pointer", fontSize: 13, fontWeight: 700, background: MI_GREEN, color: "#fff", opacity: busy ? 0.6 : 1 }}>
          Add recipient
        </button>
      </div>
      {err && <div style={{ color: "#c0392b", fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {recips.length === 0
        ? <div style={{ fontSize: 13, color: MI_MUTE }}>No recipients yet — add one above.</div>
        : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recips.map((r) => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: "#f6faf6", border: "1px solid #e3ece3", borderRadius: 8, padding: "6px 12px", flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, color: MI_GREEN, opacity: r.active ? 1 : 0.5 }}>{r.email}{r.active ? "" : " (inactive)"}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {regions && (
                    <select value={regionKeys.includes(r.region) ? r.region : (regionKeys[0] || "")} disabled={busy}
                      onChange={(e) => changeRegion(r, e.target.value)}
                      title="Which region's numbers this recipient gets"
                      style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #cdd", fontSize: 12, background: "#fff", color: MI_GREEN2 }}>
                      {regions.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                    </select>
                  )}
                  <button onClick={() => remove(r.id)} disabled={busy} style={{ padding: "4px 12px", border: "1px solid #d8a0a0", borderRadius: 6, cursor: busy ? "default" : "pointer", fontSize: 12, fontWeight: 700, background: "#fff", color: "#c0392b" }}>Remove</button>
                </span>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function MIBasisTab({ bids, d3Lib, isMobile }) {
  const allBids = bids || [];
  const hasData = useCallback(
    (source, commodity) => allBids.some((r) => r.source === source && (commodity == null || r.commodity === commodity)),
    [allBids]
  );

  // Drill: elevator → location → commodity, defaulting to whatever has data.
  const elevatorsWithData = useMemo(
    () => MI_ELEVATORS.filter((e) => e.locations.some((l) => hasData(l.source))),
    [hasData]
  );
  const [elevId, setElevId] = useState(null);
  const elevator = MI_ELEVATORS.find((e) => e.id === elevId) || elevatorsWithData[0] || MI_ELEVATORS[0];
  useEffect(() => {
    if (elevatorsWithData.length && !elevatorsWithData.some((e) => e.id === elevId)) setElevId(elevatorsWithData[0].id);
  }, [elevatorsWithData, elevId]);

  const locsWithData = useMemo(() => elevator.locations.filter((l) => hasData(l.source)), [elevator, hasData]);
  const [source, setSource] = useState(null);
  const activeSource = locsWithData.some((l) => l.source === source) ? source : (locsWithData[0] && locsWithData[0].source);
  useEffect(() => {
    if (locsWithData.length && !locsWithData.some((l) => l.source === source)) setSource(locsWithData[0].source);
  }, [locsWithData, source]);

  const commodOrder = Object.keys(MI_COMMODITY_LABELS);
  const commoditiesWithData = useMemo(
    () => [...new Set(allBids.filter((r) => r.source === activeSource).map((r) => r.commodity))]
      .sort((a, b) => (commodOrder.indexOf(a) + 1 || 99) - (commodOrder.indexOf(b) + 1 || 99)),
    [allBids, activeSource, commodOrder]
  );
  const [commodity, setCommodity] = useState(null);
  const activeCommodity = commoditiesWithData.includes(commodity) ? commodity : commoditiesWithData[0];
  useEffect(() => {
    if (commoditiesWithData.length && !commoditiesWithData.includes(commodity)) setCommodity(commoditiesWithData[0]);
  }, [commoditiesWithData, commodity]);

  const rows = useMemo(
    () => allBids.filter((r) => r.source === activeSource && r.commodity === activeCommodity),
    [allBids, activeSource, activeCommodity]
  );
  const dates = useMemo(() => [...new Set(rows.map((r) => r.bid_date))].sort(), [rows]);
  const latestDate = dates.length ? dates[dates.length - 1] : null;
  const prevDate = dates.length > 1 ? dates[dates.length - 2] : null;

  const currentBids = useMemo(() => {
    const prevByPeriod = {};
    rows.filter((r) => r.bid_date === prevDate).forEach((r) => { prevByPeriod[r.delivery_period] = r; });
    return rows
      .filter((r) => r.bid_date === latestDate)
      .map((r) => ({ ...r, prev: prevByPeriod[r.delivery_period] || null }))
      .sort((a, b) => miPeriodSortKey(a.delivery_period) - miPeriodSortKey(b.delivery_period));
  }, [rows, latestDate, prevDate]);

  const [period, setPeriod] = useState(null);
  useEffect(() => {
    if (currentBids.length && !currentBids.some((r) => r.delivery_period === period)) {
      setPeriod(currentBids[0].delivery_period);
    }
  }, [currentBids, period]);

  const chartSeries = useMemo(
    () => rows
      .filter((r) => r.delivery_period === period && r.published_basis != null)
      .map((r) => ({ date: r.bid_date, value: Number(r.published_basis) }))
      .sort((a, b) => (a.date < b.date ? -1 : 1)),
    [rows, period]
  );

  const money = (n) => (n == null || isNaN(n) ? "—" : `${n < 0 ? "−" : ""}$${Math.abs(Number(n)).toFixed(2)}`);
  const moneySigned = (n) => (n == null || isNaN(n) ? "—" : `${n < 0 ? "−" : "+"}$${Math.abs(Number(n)).toFixed(2)}`);
  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 10, padding: 16, marginBottom: 16 };
  const th = { padding: "8px 10px", textAlign: "right", fontSize: 11, color: MI_MUTE, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "2px solid #e3ece3", whiteSpace: "nowrap" };
  const thL = { ...th, textAlign: "left" };
  const td = { padding: "8px 10px", textAlign: "right", fontSize: 14, color: MI_GREEN, borderBottom: "1px solid #f0f4f0", whiteSpace: "nowrap" };
  const tdL = { ...td, textAlign: "left", fontWeight: 600 };
  const sel = { padding: 6, borderRadius: 6, border: "1px solid #ccd", fontSize: 13 };

  // elevator → location → commodity selectors (each hidden when it has one option)
  const selectors = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <select value={elevator.id} onChange={(e) => setElevId(e.target.value)} style={sel}>
        {MI_ELEVATORS.map((e) => {
          const has = e.locations.some((l) => hasData(l.source));
          return <option key={e.id} value={e.id}>{e.label} ({e.region}){has ? "" : " — no data yet"}</option>;
        })}
      </select>
      {elevator.locations.length > 1 && (
        <select value={activeSource || ""} onChange={(e) => setSource(e.target.value)} style={sel}>
          {elevator.locations.map((l) => {
            const has = hasData(l.source);
            return <option key={l.source} value={l.source} disabled={!has}>{l.label}{has ? "" : " — no data"}</option>;
          })}
        </select>
      )}
      {commoditiesWithData.length > 1 && (
        <select value={activeCommodity || ""} onChange={(e) => setCommodity(e.target.value)} style={sel}>
          {commoditiesWithData.map((c) => <option key={c} value={c}>{miCommodityLabel(c)}</option>)}
        </select>
      )}
    </div>
  );

  const activeLocLabel = (elevator.locations.find((l) => l.source === activeSource) || {}).label || "";
  const title = `${elevator.label}${activeLocLabel && elevator.locations.length > 1 ? " · " + activeLocLabel : ""} — ${miCommodityLabel(activeCommodity) || "bids"}`;

  if (!elevatorsWithData.length) {
    return (
      <div style={card}>
        <strong style={{ color: MI_GREEN2 }}>No elevator bids scraped yet.</strong>
        <div style={{ fontSize: 13, color: MI_MUTE, marginTop: 6 }}>
          Daily weekday scrapes populate this from the configured elevator boards (NY/OH/PA). Bids appear after the next successful run; the basis chart fills in as days accumulate.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Current posted bids */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          <strong style={{ color: MI_GREEN2, fontSize: 15 }}>{title}</strong>
          <span style={{ fontSize: 12, color: MI_MUTE }}>{latestDate ? `as of ${latestDate}${prevDate ? ` · Δ vs ${prevDate}` : ""}` : ""}</span>
        </div>
        <div style={{ marginBottom: 12 }}>{selectors}</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
            <thead>
              <tr>
                <th style={thL}>Delivery</th>
                <th style={th}>Bid</th>
                <th style={th}>Basis</th>
                <th style={th}>Basis Δ</th>
                <th style={th}>Futures</th>
                <th style={th}>Chg</th>
                <th style={{ ...th, textAlign: "left" }}>Futures Mo.</th>
              </tr>
            </thead>
            <tbody>
              {currentBids.map((r) => {
                const dB = r.prev && r.prev.published_basis != null && r.published_basis != null
                  ? Number(r.published_basis) - Number(r.prev.published_basis) : null;
                return (
                  <tr key={r.delivery_period}>
                    <td style={tdL}>{r.notes || r.delivery_period}</td>
                    <td style={td}>{money(r.cash_price)}</td>
                    <td style={td}>{moneySigned(r.published_basis)}</td>
                    <td style={{ ...td, color: dB == null ? MI_MUTE : miDeltaColor(dB), fontWeight: 700 }}>{dB == null ? "—" : moneySigned(dB)}</td>
                    <td style={td}>{money(r.futures_price)}</td>
                    <td style={{ ...td, color: r.futures_change == null ? MI_MUTE : miDeltaColor(r.futures_change) }}>
                      {r.futures_change == null ? "—" : `${r.futures_change > 0 ? "+" : ""}${Number(r.futures_change).toFixed(2)}`}
                    </td>
                    <td style={{ ...td, textAlign: "left", color: MI_MUTE }}>{r.futures_month || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: MI_MUTE, marginTop: 8 }}>
          Basis = cash bid − futures, as posted by the elevator. "Chg" is the board's daily futures change.
        </div>
      </div>

      {/* Historical basis chart */}
      <div style={card}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <strong style={{ color: MI_GREEN2, fontSize: 15 }}>Basis history</strong>
          <select value={period || ""} onChange={(e) => setPeriod(e.target.value)} style={{ padding: 6, borderRadius: 6, border: "1px solid #ccd" }}>
            {currentBids.map((r) => (
              <option key={r.delivery_period} value={r.delivery_period}>{r.notes || r.delivery_period}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: MI_MUTE }}>posted basis ($/bu) over time</span>
        </div>
        {chartSeries.length > 1
          ? <MILineChart series={chartSeries} d3Lib={d3Lib} isMobile={isMobile} color="#2d6a1a" />
          : <div style={{ color: MI_MUTE, fontSize: 13, padding: "24px 0" }}>Not enough history yet for this delivery month — the chart builds as the daily scrape accumulates points.</div>}
      </div>

      {isCurrentUserAdmin() && <MIRecipientManager
        table="basis_alert_recipients"
        title="Basis-change alerts"
        blurb="Recipients get an email whenever any posted basis changes vs the prior business day — across every scraped elevator board (NY/OH/PA, all locations and commodities) — sent automatically after each daily scrape."
      />}
    </div>
  );
}

// ── BRIEF TAB ────────────────────────────────────────────────────────────────
// Daily WNY corn Market Intelligence brief: live preview (server-rendered via
// /api/market?fn=brief&dryRun=1), a test-send, and the recipient manager. The
// brief itself is sent by a weekday cron; this tab is for managing + previewing.
function MIBriefTab({ isMobile }) {
  const [preview, setPreview] = useState("");
  const [loadingPrev, setLoadingPrev] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [testing, setTesting] = useState(false);
  // The regions come from the model (MI_REGIONS), which is also what the brief
  // renders off and what the region column is checked against — so this picker
  // cannot offer a region the email can't produce.
  const regionOpts = useMemo(() => Object.entries(MI_REGIONS).map(([key, v]) => ({ key, label: v.label, hint: v.hint })), []);
  const [region, setRegion] = useState(regionOpts[0].key);

  const loadPreview = useCallback(() => {
    setLoadingPrev(true);
    fetch(`/api/market?fn=brief&dryRun=1&region=${encodeURIComponent(region)}`, { headers: getApiAuthHeaders() })
      .then((r) => r.json())
      .then((j) => setPreview(j.html || ""))
      .catch(() => setPreview(""))
      .finally(() => setLoadingPrev(false));
  }, [region]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const sendTest = async () => {
    const email = testEmail.trim().toLowerCase();
    setTestMsg("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setTestMsg("Enter a valid email to send a test to."); return; }
    setTesting(true);
    try {
      const res = await fetch(`/api/market?fn=brief&force=1&test=${encodeURIComponent(email)}&region=${encodeURIComponent(region)}`, { method: "POST", headers: getApiAuthHeaders() });
      const j = await res.json();
      const label = (MI_REGIONS[region] || {}).label || region;
      setTestMsg(j.sent ? `${label} brief sent to ${email}.` : `Not sent: ${j.reason || j.error || "unknown"}`);
    } catch (e) { setTestMsg("Send failed: " + e.message); }
    finally { setTesting(false); }
  };
  const regionBtn = (r) => ({
    padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
    border: "1px solid " + (region === r.key ? MI_GREEN : "#cdd"),
    background: region === r.key ? MI_GREEN : "#fff",
    color: region === r.key ? "#fff" : MI_GREEN2,
  });

  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 10, padding: 16, marginBottom: 16 };
  return (
    <div>
      <div style={card}>
        <strong style={{ color: MI_GREEN2, fontSize: 15 }}>Daily corn brief</strong>
        <div style={{ fontSize: 13, color: MI_MUTE, margin: "4px 0 0" }}>
          A data-only, corn-focused one-pager emailed to recipients every weekday morning (~6 AM ET). Sections lead with planting pace now and shift to crop conditions, growing-season weather, then harvest & marketing as the season progresses — and any section without data yet (e.g. crop conditions before mid-June) is hidden until it lands.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
          <span style={{ fontSize: 12, color: MI_MUTE, fontWeight: 700 }}>Region</span>
          {regionOpts.map((r) => (
            <button key={r.key} onClick={() => setRegion(r.key)} style={regionBtn(r)}>{r.label}</button>
          ))}
          <span style={{ fontSize: 11, color: MI_MUTE }}>
            {(MI_REGIONS[region] || {}).hint} · sets the preview and the test send below
          </span>
        </div>
        <div style={{ fontSize: 11, color: MI_MUTE, marginTop: 6 }}>
          Both views come out of one daily model run — the yield, production and county range narrow to the region; cash and basis are WNY Energy's board either way. Each recipient below gets the region set beside their name.
        </div>
      </div>

      {isCurrentUserAdmin() && <MIRecipientManager
        table="mi_brief_recipients"
        title="Brief recipients"
        blurb="Everyone here gets the weekday morning brief, in the region set beside their address. This list is independent of the basis-change alerts."
        regions={regionOpts}
      />}

      {isCurrentUserAdmin() && <div style={card}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <strong style={{ color: MI_GREEN2, fontSize: 15, marginRight: 4 }}>Send a test</strong>
          <input type="email" value={testEmail} placeholder="you@example.com"
            onChange={(e) => setTestEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendTest(); }}
            style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #ccd", minWidth: 220, fontSize: 14 }} />
          <button onClick={sendTest} disabled={testing} style={{ padding: "8px 16px", border: "none", borderRadius: 8, cursor: testing ? "default" : "pointer", fontSize: 13, fontWeight: 700, background: MI_GREEN, color: "#fff", opacity: testing ? 0.6 : 1 }}>
            {testing ? "Sending…" : "Send test"}
          </button>
          {testMsg && <span style={{ fontSize: 12, color: MI_MUTE }}>{testMsg}</span>}
        </div>
        <div style={{ fontSize: 11, color: MI_MUTE, marginTop: 6 }}>Sends today's brief to that one address only — recipients above are untouched.</div>
      </div>}

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <strong style={{ color: MI_GREEN2, fontSize: 15 }}>Live preview</strong>
          <button onClick={loadPreview} style={{ padding: "6px 12px", border: "1px solid #cdd", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700, background: "#fff", color: MI_GREEN2 }}>Refresh</button>
        </div>
        {loadingPrev
          ? <div style={{ color: MI_MUTE, fontSize: 13, padding: "20px 0" }}>Building preview…</div>
          : preview
            ? <iframe title="brief preview" srcDoc={preview} style={{ width: "100%", height: isMobile ? 480 : 640, border: "1px solid #e3ece3", borderRadius: 8, background: "#fff" }} />
            : <div style={{ color: MI_MUTE, fontSize: 13, padding: "20px 0" }}>Preview unavailable — the brief endpoint may not be deployed yet.</div>}
      </div>
    </div>
  );
}

// County corn-yield estimator. Reads /api/market?fn=yield-estimate (trend baseline ×
// weather anomaly × statewide pace × optional manual override). Honest:
// it surfaces the disclaimer + per-county confidence + climatology-fill flags.
// ── The corn yield model moved to src/miYieldModel.js ───────────────────────
// (imported at the top of this file). It is shared verbatim with the daily
// cron in api/_mi-snapshot.js so the emailed numbers cannot drift from these.

// ── Growth stage rail ───────────────────────────────────────────────────────
// Where the crop is right now, along the whole season, driven by GDD accumulated
// SINCE THE FIELD'S OWN PLANTING DATE — not a county-generic calendar.
//
// Segments are proportional to their real GDD width, so grain fill reads as the long
// haul it is and pre-emergence as the sliver it is. The current stage is highlighted
// as a BAND rather than a needle: GDD alone doesn't know hybrid maturity, so the
// honest claim is "in this stage", not "at this exact point".
//
// Renders nothing rather than something wrong: no plant date, a window that starts
// after planting, or a crop without a calibrated table all fall back to a stated
// reason. Never assume a planting date.
function StageRail({ crop, gdd, plantISO, unavailable }) {
  const supported = MI_STAGE_HAS_TABLE(crop);
  if (!supported || unavailable || gdd == null) {
    const why = !supported
      ? `No calibrated growth-stage model for ${String(crop || "this crop").toLowerCase()} — corn and soybeans only.`
      : unavailable;
    return (
      <div style={{ border:"1px solid #e8eee8", borderRadius:9, background:"#fafcfa", padding:"9px 12px", fontSize:11, color:"#60735f" }}>
        <b style={{ color:"#5c6f5c" }}>Growth stage</b> — {why}
      </div>
    );
  }
  const { table, idx } = MI_STAGE_AT(gdd, crop);
  const stages = table.stages;
  const TAIL = table.mature * 0.10;                 // open-ended "mature" tail
  const span = table.mature + TAIL;
  const frac = stages.map((s, i) => {
    const lo = i === 0 ? 0 : stages[i - 1].to;
    const hi = s.to === Infinity ? span : s.to;
    return Math.max(0, (hi - lo) / span);
  });
  const pct = Math.min(100, Math.max(0, (gdd / span) * 100));
  const cur = stages[idx];
  const toMature = Math.max(0, Math.round(table.mature - gdd));

  return (
    <div style={{ border:"1px solid #e0ece0", borderRadius:10, background:"#fbfdfb", padding:"11px 13px" }}>
      <div style={{ display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap", marginBottom:7 }}>
        <span style={{ fontSize:10, textTransform:"uppercase", letterSpacing:".05em", color:"#677167", fontWeight:800 }}>Growth stage</span>
        <span style={{ fontSize:13.5, fontWeight:800, color:"#1f5c2e" }}>{cur.label}</span>
        <span style={{ fontSize:11, color:"#60735f" }}>
          {Math.round(gdd).toLocaleString()} GDD since {plantISO ? plantISO.slice(5).replace("-", "/") : "planting"}
          {toMature > 0 ? ` · ${toMature.toLocaleString()} to maturity` : " · at maturity"}
        </span>
      </div>

      <div style={{ display:"flex", gap:2, position:"relative" }}>
        {stages.map((s, i) => {
          const done = i < idx, now = i === idx;
          const wide = frac[i] >= 0.11;              // only label a segment that fits
          return (
            <div key={s.short}
                 title={`${s.label} — ${i === 0 ? 0 : stages[i-1].to} to ${s.to === Infinity ? "…" : s.to} GDD`}
                 style={{ flex:`${Math.max(frac[i], 0.03)} 0 0`, height:22, borderRadius:3,
                          background: now ? "#2d6a2d" : done ? "#cfe3cf" : "#eef3ee",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          overflow:"hidden", minWidth:0 }}>
              {wide && (
                <span style={{ fontSize:10.5, fontWeight:800, whiteSpace:"nowrap",
                               color: now ? "#fff" : done ? "#22331f" : "#5c6f5c" }}>{s.short}</span>
              )}
            </div>
          );
        })}
        {/* position tick — deliberately thin; the BAND is the claim, this is a hint */}
        <div style={{ position:"absolute", left:`${pct}%`, top:-3, bottom:-3, width:2,
                      background:"#1a2e1a", opacity:0.45, borderRadius:1, pointerEvents:"none" }} />
      </div>

      <div style={{ fontSize:10, color:"#677167", marginTop:7, lineHeight:1.5 }}>
        Approximate — from growing degree days accumulated since <b>this field's own planting date</b>, on its nearest ~4 km grid cell.
        Hybrid maturity isn't modeled, so read the highlighted <b>band</b>, not the tick.
      </div>
    </div>
  );
}

// Compact inline-SVG line chart for the Model Lab (no D3 dependency).
// series: [{ name, color, dash?, w?, vals:[number|null] }] aligned to xLabels.
function MiKnob({ label, val, min, max, step, fmt, hint, onChange }) {
  return (
    <label style={{ display: "block", fontSize: 11, color: "#6b7e5e" }}>
      <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span style={{ fontWeight: 700, color: "#1a2e1a" }}>{label}</span><b style={{ color: "#2d6a2d" }}>{fmt ? fmt(val) : val}</b></span>
      <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => onChange(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#2d6a2d" }} />
      {hint && <div style={{ fontSize: 10.5, color: "#9ab", lineHeight: 1.35, marginTop: -1 }}>{hint}</div>}
    </label>
  );
}
// fcFrom = index of the FIRST forecast point in series[0] (the current-year
// line); everything from there on renders dashed/translucent with a "today"
// marker at the last observed point. fmtVal formats axis + tooltip values.
// Click/tap a point to pin a tooltip (date + each series value); hover previews.
function MiChart({ title, sub, xLabels, series, fcFrom = null, fmtVal, big = false, fitY = false }) {
  const W = 520, H = big ? 330 : 158, padL = 42, padR = 12, padT = 14, padB = 24;
  const [hover, setHover] = React.useState(null);
  const [pinned, setPinned] = React.useState(null);
  const svgRef = React.useRef(null);
  const all = (series || []).flatMap((s) => (s.vals || []).filter((v) => v != null));
  if (!all.length || !(xLabels || []).length) {
    return <div style={{ flex: big ? "1 1 100%" : "1 1 250px", minWidth: big ? "100%" : 250, fontSize: 11, color: "#9ab", padding: "18px 0" }}>{title}: no data yet</div>;
  }
  const n = xLabels.length;
  // Default: zero-anchored axis (right for cumulative series like GDD/rainfall).
  // fitY: zoom the axis to the data range so small day-to-day moves are legible
  // (right for a tight series like a yield prediction that hovers ~160 bu).
  const dataMin = Math.min(...all), dataMax = Math.max(...all);
  let ymin = 0, ymax = (dataMax * 1.08) || 1;
  if (fitY) {
    const span = dataMax - dataMin;
    const pad = span > 0 ? span * 0.25 : Math.max(Math.abs(dataMax) * 0.05, 1);
    ymin = dataMin - pad;
    ymax = dataMax + pad;
  }
  const cW = W - padL - padR, cH = H - padT - padB;
  const xp = (i) => padL + (n < 2 ? cW / 2 : (i / (n - 1)) * cW);
  const yp = (v) => padT + cH - ((v - ymin) / ((ymax - ymin) || 1)) * cH;
  const vfmt = fmtVal || ((v) => Math.round(v).toLocaleString());
  // path over indices in [lo,hi] (inclusive); breaks across null gaps
  const path = (vals, lo = 0, hi = n - 1) => {
    let d = "", pen = false;
    (vals || []).forEach((v, i) => {
      if (v == null || i < lo || i > hi) { pen = false; return; }
      d += (pen ? "L" : "M") + xp(i).toFixed(1) + "," + yp(v).toFixed(1) + " "; pen = true;
    });
    return d.trim();
  };
  const fb = fcFrom != null && fcFrom > 0 && fcFrom < n ? fcFrom : null; // forecast boundary
  const active = pinned != null ? pinned : hover;
  const idxFromEvent = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    return Math.max(0, Math.min(n - 1, Math.round(((fx * W - padL) / cW) * (n - 1))));
  };
  return (
    <div style={{ flex: big ? "1 1 100%" : "1 1 250px", minWidth: big ? "100%" : 250 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2e1a" }}>{title}</div>
      {sub && <div style={{ fontSize: 10, color: "#9ab", marginBottom: 2 }}>{sub}</div>}
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block", cursor: "pointer", touchAction: "none" }}
        onMouseMove={(e) => setHover(idxFromEvent(e))}
        onMouseLeave={() => setHover(null)}
        onClick={(e) => { const i = idxFromEvent(e); setPinned((p) => (p === i ? null : i)); }}>
        {[0, 0.5, 1].map((f) => { const v = ymin + (ymax - ymin) * f; return (<g key={f}><line x1={padL} x2={W - padR} y1={yp(v).toFixed(1)} y2={yp(v).toFixed(1)} stroke="#eef3ee" /><text x={padL - 4} y={(yp(v) + 3).toFixed(1)} textAnchor="end" fontSize="8" fill="#9ab">{vfmt(v)}</text></g>); })}
        {fb != null && <line x1={xp(fb - 1).toFixed(1)} x2={xp(fb - 1).toFixed(1)} y1={padT} y2={padT + cH} stroke="#cbb26b" strokeWidth="1" strokeDasharray="2,2" />}
        {series.map((s, si) => {
          if (si === 0 && fb != null) {
            return (<g key={si}>
              <path d={path(s.vals, 0, fb - 1)} fill="none" stroke={s.color} strokeWidth={s.w || 2} strokeLinejoin="round" strokeLinecap="round" />
              <path d={path(s.vals, fb - 1, n - 1)} fill="none" stroke={s.color} strokeWidth={s.w || 2} strokeDasharray="4,3" opacity="0.6" strokeLinejoin="round" strokeLinecap="round" />
            </g>);
          }
          return <path key={si} d={path(s.vals)} fill="none" stroke={s.color} strokeWidth={s.w || 2} strokeDasharray={s.dash || "none"} strokeLinejoin="round" />;
        })}
        {active != null && (() => {
          const gx = xp(active);
          const rows = series.map((s) => ({ name: s.name, color: s.color, v: s.vals ? s.vals[active] : null })).filter((r) => r.v != null);
          if (!rows.length) return null;
          const isFc = fb != null && active >= fb && series[0] && series[0].vals && series[0].vals[active] != null;
          const lineH = 12, boxW = 108, boxH = 15 + rows.length * lineH + (isFc ? 10 : 0);
          let bx = gx + 8; if (bx + boxW > W - 2) bx = gx - boxW - 8; if (bx < 2) bx = 2;
          const by = padT + 2;
          return (<g>
            <line x1={gx.toFixed(1)} x2={gx.toFixed(1)} y1={padT} y2={padT + cH} stroke="#9ab" strokeWidth="1" />
            {rows.map((r, ri) => <circle key={ri} cx={gx.toFixed(1)} cy={yp(r.v).toFixed(1)} r="3" fill={r.color} stroke="#fff" strokeWidth="1" />)}
            <rect x={bx} y={by} width={boxW} height={boxH} rx="4" fill="#fff" stroke="#ccd6cc" opacity="0.97" />
            <text x={bx + 6} y={by + 11} fontSize="8.5" fontWeight="700" fill="#1a2e1a">{xLabels[active]}</text>
            {rows.map((r, ri) => (<text key={ri} x={bx + 6} y={by + 11 + (ri + 1) * lineH} fontSize="8.5" fill="#3a4a3a"><tspan fill={r.color}>●</tspan> {r.name}: {vfmt(r.v)}</text>))}
            {isFc && <text x={bx + 6} y={by + 11 + (rows.length + 1) * lineH} fontSize="7.5" fontStyle="italic" fill="#b88500">forecast</text>}
          </g>);
        })()}
        {xLabels.map((lb, i) => ((n <= 9 || (i % Math.ceil(n / 8) === 0 && n - 1 - i >= Math.ceil(n / 8) / 2) || i === n - 1) ? <text key={i} x={xp(i).toFixed(1)} y={H - 6} textAnchor="middle" fontSize="8" fill="#9ab">{lb}</text> : null))}
      </svg>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10, color: "#6b7e5e", marginTop: 2 }}>
        {series.map((s, si) => <span key={si} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="16" height="6"><line x1="0" y1="3" x2="16" y2="3" stroke={s.color} strokeWidth="2" strokeDasharray={s.dash || "none"} /></svg>{s.name}</span>)}
        {fb != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#b88500" }}><svg width="16" height="6"><line x1="0" y1="3" x2="16" y2="3" stroke="#cbb26b" strokeWidth="2" strokeDasharray="4,3" /></svg>forecast</span>}
        <span style={{ color: "#9ab" }}>· tap a point for values</span>
      </div>
    </div>
  );
}

// ── Interactive WNY county map for yield prediction ────────────────────
// Finger Lakes — drawn as water polygons OVER the choropleth so the long
// glacial lakes (no corn on them) read as water, not interpolated yield. The
// Great Lakes don't need explicit geometry: they're outside the county clip,
// where the map's blue background already shows as water. Each lake is an
// approximate axis-aligned ribbon (N-end → S-end, real-ish width in degrees)
// in [lon,lat]; projected through the same geoPath as the counties. Order
// roughly west→east. Good enough for orientation, not nautical charts.
const FINGER_LAKES = {
  type: "FeatureCollection",
  features: [
    ["Conesus",      [-77.715, 42.81], [-77.722, 42.69], 0.012],
    ["Hemlock",      [-77.607, 42.78], [-77.598, 42.63], 0.010],
    ["Honeoye",      [-77.520, 42.80], [-77.512, 42.71], 0.010],
    ["Canadice",     [-77.575, 42.74], [-77.572, 42.66], 0.008],
    ["Canandaigua",  [-77.285, 42.885],[-77.265, 42.655], 0.018],
    ["Keuka",        [-77.090, 42.605],[-77.130, 42.385], 0.018],
    ["Seneca",       [-76.960, 42.875],[-76.900, 42.355], 0.026],
    ["Cayuga",       [-76.742, 42.930],[-76.700, 42.455], 0.022],
    ["Owasco",       [-76.530, 42.930],[-76.512, 42.775], 0.014],
    ["Skaneateles",  [-76.435, 42.950],[-76.405, 42.785], 0.014],
  ].map(([name, n, s, halfW]) => ({
    type: "Feature",
    properties: { name },
    geometry: {
      type: "Polygon",
      // 4-corner ribbon: NW, NE, SE, SW (half-width perpendicular ≈ in lon degrees)
      coordinates: [[
        [n[0] - halfW, n[1]], [n[0] + halfW, n[1]],
        [s[0] + halfW, s[1]], [s[0] - halfW, s[1]],
        [n[0] - halfW, n[1]],
      ]],
    },
  })),
};

// MRMS Q3 radar color → rainfall (inches) lookup, decoded directly from IEM's own
// MRMS Q3 24h legend (mrms_q3_p24h.png); values interpolated across its labeled
// breakpoints (0.25/0.5/0.75/1/2/3/4/5/7/10/15"). Verified the 48h/72h WMS layers
// share this colortable (98.8% pixel match), so one table covers all radar windows.
// Each entry: [R, G, B, inches]. Used to read the value under the cursor.
const MRMS_RAMP_LUT = [[0,0,0,0.25],[0,24,255,0.28],[0,42,255,0.31],[0,60,255,0.34],[0,78,255,0.37],[0,96,255,0.4],[0,114,255,0.43],[0,132,255,0.46],[0,150,255,0.49],[0,168,255,0.52],[0,186,255,0.55],[0,204,255,0.58],[0,254,240,0.61],[0,254,222,0.64],[0,254,204,0.67],[0,254,186,0.69],[0,254,168,0.72],[0,254,150,0.75],[0,254,132,0.78],[0,254,114,0.81],[0,254,96,0.84],[0,254,78,0.87],[0,254,60,0.9],[0,254,42,0.93],[0,254,24,0.96],[0,254,6,0.99],[12,254,0,1.08],[30,254,0,1.2],[48,254,0,1.32],[66,254,0,1.44],[84,254,0,1.56],[102,254,0,1.68],[120,254,0,1.79],[138,254,0,1.91],[156,254,0,2.03],[174,254,0,2.15],[192,254,0,2.27],[210,254,0,2.39],[228,254,0,2.51],[254,228,0,2.62],[254,210,0,2.74],[254,192,0,2.86],[254,174,0,2.98],[254,156,0,3.1],[254,138,0,3.22],[254,120,0,3.34],[254,102,0,3.45],[254,84,0,3.57],[254,66,0,3.69],[254,48,0,3.81],[254,30,0,3.93],[254,12,0,4.05],[254,0,0,4.17],[254,0,18,4.4],[254,0,36,4.52],[254,0,54,4.64],[254,0,72,4.76],[254,0,90,4.88],[254,0,108,5],[254,0,126,5.23],[254,0,144,5.47],[254,0,162,5.7],[254,0,180,5.94],[254,0,198,6.18],[254,0,254,6.42],[254,12,254,6.65],[254,24,254,6.89],[254,36,254,7.19],[254,48,254,7.55],[254,60,254,7.9],[254,72,254,8.26],[254,84,254,8.61],[254,96,254,8.97],[254,108,254,9.32],[254,120,254,9.68],[254,132,254,10.06],[254,144,254,10.65],[254,156,254,11.25],[254,168,254,11.84],[254,180,254,12.43],[254,192,254,13.02],[254,204,254,13.62],[254,216,254,14.21],[254,228,254,14.8]];
// inches → [R,G,B] via the SAME MRMS colortable (nearest entry with inches ≤ v), so a
// client render of our exact 1km raingrid decodes back through MRMS_RAMP_LUT identically
// to IEM's WMS — hover/legend keep working unchanged. Below 0.25" → transparent (null).
function mrmsLutColor(v) {
  if (v == null || v < MRMS_RAMP_LUT[0][3]) return null;
  let c = MRMS_RAMP_LUT[0];
  for (let i = 1; i < MRMS_RAMP_LUT.length; i++) { if (MRMS_RAMP_LUT[i][3] <= v) c = MRMS_RAMP_LUT[i]; else break; }
  return c;
}
// Pattern lifted from MapView (market share map). TopoJSON + d3 projection
// + inline SVG. Filters to the 16 WNY counties. User picks a metric to
// color by (yield, precip, GDD anomaly, DSCI, stress drag, production).
// Click a county to focus it. Hover for a tooltip.
// Aggregate the model's per-county output to the elevator draw area (a Set of
// UPPERCASE county names; empty = all WNY) + a GDD-paced harvest-timing read.
// Pure — used by the on-map draw-area readout.
function miComputeDrawArea(data, sel) {
  const counties = ((data && data.counties) || []).filter((c) => !c.error && c.name);
  if (!counties.length) return null;
  const chosen = sel && sel.size ? counties.filter((c) => sel.has(String(c.name).toUpperCase())) : counties;
  if (!chosen.length) return null;
  let yN = 0, yD = 0, prod = 0, norm = 0, acres = 0;
  for (const c of chosen) {
    if (c.forecast_yield != null && c.pred_acres > 0) { yN += c.forecast_yield * c.pred_acres; yD += c.pred_acres; }
    if (c.pred_prod != null) prod += c.pred_prod;
    if (c.normal_prod != null) norm += c.normal_prod;
    if (c.pred_acres != null) acres += c.pred_acres;
  }
  let wsum = 0, dsum = 0;
  for (const w of ((data.cohort_summary && data.cohort_summary.weekly) || [])) {
    const d = w.plant_week ? new Date(w.plant_week) : null;
    if (d && !isNaN(d.getTime()) && w.acres > 0) { dsum += d.getTime() * w.acres; wsum += w.acres; }
  }
  let plantDate = wsum > 0 ? new Date(dsum / wsum) : null, assumed = false;
  if (!plantDate || isNaN(plantDate.getTime())) { plantDate = new Date((data.year || new Date().getFullYear()), 4, 15); assumed = true; }
  const gddAnom = data.wx_pulse && data.wx_pulse.season_gdd_anom != null ? data.wx_pulse.season_gdd_anom : 0;
  const adjDays = Math.round(115 / (1 + gddAnom));
  const bl = new Date(plantDate); bl.setDate(bl.getDate() + adjDays);
  const h0 = new Date(bl); h0.setDate(h0.getDate() + 21);
  const h1 = new Date(bl); h1.setDate(h1.getDate() + 35);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return {
    count: chosen.length, total: counties.length, isAll: chosen.length === counties.length,
    yieldWavg: yD > 0 ? yN / yD : null, prod, norm, acres,
    vsNorm: norm > 0 ? (prod - norm) / norm : null,
    harvest: { bl, h0, h1, fmt, gddAnom, assumed },
  };
}

// ── WHICH COUNTIES A LAYER CAN SPEAK FOR ─────────────────────────────────────
//
// The map used to draw exactly MI_MODEL_COUNTIES (39) on every layer, because
// there was one county list and the yield model owned it. That is right for the
// yield/production layers — a county joins those only at full parity, >= 6 NASS
// grain-yield years, and Clinton has ONE (see miYieldModel.js). It is wrong for
// every layer whose numbers never touch the yield model.
//
// Corn density is USDA CDL acreage. Soil is SSURGO. The rainfall/GDD/heat/freeze
// surfaces are gridMET, and the dry-down / disease layers read per-cell values
// off gridShadow, which is built from an UNFILTERED wny_grid_cells read. All of
// them resolve per CELL, and every county with cells has them — measured
// 2026-08-20: 1,271 cells over 51 counties, full season-anomaly coverage on all
// 51, soil on 1,269.
//
// So the 39-county gate was withholding ~12 layers from 12 counties for a reason
// that applies to 4 of them. The counties it hid are not marginal: CLINTON is
// 24,269 CDL corn acres (more than Erie or Wayne, both drawn) and FRANKLIN
// 20,784. They fail the yield bar because NASS publishes them as SILAGE country
// — Clinton has 10 straight years of county silage yield and one grain year —
// which is the bar measuring the thing those counties do not do.
//
// THE STATE OUTLINE IS NOW COMPLETE ON EVERY LAYER AND THE DATA GATE MOVED TO
// THE FILL. A county the active layer cannot speak for is drawn and hatched as
// no-data, never omitted: omitting it made the map's own extent a claim about
// where corn grows, and it reframed the projection every time the metric
// changed. Hatching also stops the IDW surface bleeding a number into ground it
// was never fit on, which is the guard `needsCorn` already relied on.
const MI_CELL_DATA_LAYERS = new Set([
  // CDL / SSURGO — no model, no NASS history
  "corn_density", "soil_nccpi", "soil_awc",
  // gridMET per-cell weather surfaces. `precip_pct_normal` is the same shape but
  // is not offered in the picker, so it is not listed — check-mi-layers refuses a
  // registry key the UI never shows.
  "precip_pct_window", "forecast_rain", "gdd_pct_normal",
  "precip_in_window", "season_gdd_grid", "heat_days_grid", "freeze_days_grid",
  // per-cell layers off gridShadow (cell_water / cell_disease), which the model
  // fills for EVERY cell it has daily+climatology for, not just modelled counties
  "soil_water_stress", "disease_pressure",
  // MRMS radar images — an image overlay clipped to the drawn counties; it knows
  // nothing about counties at all, so it can cover the whole state
  "radar_p24h", "radar_p48h", "radar_p72h",
]);
// The five NYC boroughs. Excluded by Kevin's call (2026-08-20, "complete map of
// NY, except for maybe NYC") and not by a data test — Bronx/Kings/New York/
// Queens/Richmond carry no corn in any source we hold. Kept as an explicit,
// named list so the next reader sees a decision rather than a gap.
const MI_NYC_FIPS = new Set(["36005", "36047", "36061", "36081", "36085"]);

function MIYieldMap({ counties, gridShadow, isMobile, catchment, setCatchment, data, region, setRegion, regions, onNeedCellLayers, cellLayersState, isPortal = false }) {
  const [metric, setMetric] = React.useState("yield_hd");
  const [normalYears, setNormalYears] = React.useState(5);  // window for "% of normal" production map
  // ── Elevator supply lens: compare the current-year projection to selected
  //    historical year(s). cmpYears = the year(s) to average over. histRows =
  //    per-county historical yield (RMA final 2023-25, NASS fallback ≤2022) +
  //    NASS grain harvested acres (silage excluded) for production = yld × acres.
  const ELEV_METRICS = ["hist_yield", "hist_prod", "yield_vs_cmp", "prod_vs_cmp"];
  const [cmpYears, setCmpYears] = React.useState([2025]);
  const [histRows, setHistRows] = React.useState(null);   // { [COUNTY]: { rma:{yr:y}, nass:{yr:y}, grainAc:{yr:ac} } }
  const toggleCmpYear = useCallback((y) => {
    setCmpYears((prev) => prev.includes(y) ? (prev.length > 1 ? prev.filter((x) => x !== y) : prev) : [...prev, y].sort());
  }, []);
  const [hovered, setHovered] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  // Elevator draw-area picker: when on, clicking a county toggles it in/out of
  // the catchment (Set of names) instead of opening the detail panel. The
  // catchment highlights on the map at all times so the draw area stays visible.
  const [drawMode, setDrawMode] = React.useState(false);
  const cat = catchment || (typeof window !== "undefined" && new Set());
  const toggleCatch = useCallback((nm) => {
    if (!setCatchment) return;
    setCatchment((prev) => { const n = new Set(prev || []); n.has(nm) ? n.delete(nm) : n.add(nm); return n; });
  }, [setCatchment]);
  // Draw-area supply readout (shown below the map when counties are selected).
  const drawSummary = (cat && cat.size && data) ? miComputeDrawArea(data, cat) : null;
  // Editable-window rainfall % of normal (gridMET daily ÷ daily climatology via RPC)
  const [precipWin, setPrecipWin] = React.useState({ preset: "season", from: "", to: "" });
  const [windowPct, setWindowPct] = React.useState({});  // "lat|lon" → % of normal
  const [windowIn, setWindowIn] = React.useState({});    // "lat|lon" → actual rainfall (in) over the window
  const [windowSummary, setWindowSummary] = React.useState(null); // region avg actual/normal/pct
  // Latest day with real (non-forecast) precip in the grid. We clamp window ends to
  // this so a "through today" preset never divides a short actual span by a full-length
  // normal (the old apples-to-oranges bug). MRMS keeps this ~1 day fresher than gridMET.
  const [dataThrough, setDataThrough] = React.useState(null);
  React.useEffect(() => {
    fetch(`${SUPABASE_URL}/rest/v1/wny_grid_weather_daily?select=obs_date&is_forecast=eq.false&order=obs_date.desc&limit=1`, { headers: getAuthHeaders() })
      .then((r) => r.json()).then((d) => { if (d && d[0]?.obs_date) setDataThrough(d[0].obs_date); }).catch(() => {});
  }, []);
  // Lazy-load historical yields/acres the first time an elevator metric is picked.
  React.useEffect(() => {
    if (histRows || !ELEV_METRICS.includes(metric)) return;
    let cancelled = false;
    Promise.all([
      // rma_county_yields_public, not the base table: the table carries
      // book_acres + book_farmers (our own book, per county) and this screen is
      // portal-reachable via can_view_mi. The view drops those two columns; the
      // base table is admin-only. See sql/012. New York only: the table holds every
      // state, and keyed by county name alone Seneca read Ohio's and Wayne and
      // Wyoming read Pennsylvania's (fixed 2026-11-03).
      fetch(`${SUPABASE_URL}/rest/v1/rma_county_yields_public?crop=eq.CORN&state=eq.NY&select=crop_year,county,rma_final_yield`, { headers: getAuthHeaders() }).then((r) => r.json()),
      fetch(`${SUPABASE_URL}/rest/v1/nass_data?crop=eq.CORN&state=eq.NY&stat_type=in.(YIELD,ACRES_HARVESTED)&county=not.is.null&select=year,county,stat_type,value&limit=3000`, { headers: getAuthHeaders() }).then((r) => r.json()),
    ]).then(([rma, nass]) => {
      if (cancelled) return;
      const H = {}; const get = (nm) => (H[nm] || (H[nm] = { rma: {}, nass: {}, grainAc: {} }));
      for (const r of (rma || [])) { if (r.county && r.rma_final_yield != null) get(String(r.county).toUpperCase()).rma[r.crop_year] = Number(r.rma_final_yield); }
      for (const r of (nass || [])) {
        if (!r.county || r.value == null) continue;
        const o = get(String(r.county).toUpperCase());
        if (r.stat_type === "YIELD") o.nass[r.year] = Number(r.value);
        else if (r.stat_type === "ACRES_HARVESTED") o.grainAc[r.year] = Number(r.value);
      }
      setHistRows(H);
    }).catch(() => setHistRows({}));
    return () => { cancelled = true; };
  }, [metric, histRows]);
  const winDates = React.useMemo(() => {
    const today = new Date(); const iso = (d) => d.toISOString().slice(0, 10); const y = today.getFullYear();
    const end = dataThrough && dataThrough < iso(today) ? dataThrough : iso(today); // never claim data past what's loaded
    if (precipWin.preset === "custom") return { from: precipWin.from, to: precipWin.to };
    // Trailing N-day windows (2w/30d) — last N days, clamped to the latest loaded day.
    const dayWins = { "2w": 14, "30d": 30 };
    if (dayWins[precipWin.preset]) { const f = new Date(today); f.setDate(f.getDate() - dayWins[precipWin.preset]); return { from: iso(f), to: end }; }
    if (precipWin.preset === "may1") return { from: `${y}-05-01`, to: end };
    return { from: `${y}-04-20`, to: end }; // season — aligned with the yield model's growing season (MI_SEASON.start 04-20)
  }, [precipWin, dataThrough]);
  React.useEffect(() => {
    if (metric !== "precip_pct_window" && metric !== "precip_in_window") return;
    const { from, to } = winDates; if (!from || !to) return;
    let cancelled = false;
    fetch(`${SUPABASE_URL}/rest/v1/rpc/wny_precip_pct_normal`, {
      method: "POST", headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ d_start: from, d_end: to }),
    }).then((r) => r.json()).then((data) => {
      if (cancelled) return;
      const m = {}, mi = {}; let sa = 0, sn = 0, n = 0;
      for (const row of data || []) {
        const k = `${Number(row.lat).toFixed(2)}|${Number(row.lon).toFixed(2)}`;
        if (row.pct != null) m[k] = Number(row.pct);
        if (row.actual_mm != null) mi[k] = Number(row.actual_mm) / 25.4;   // rainfall inches over the window
        if (row.actual_mm != null && row.normal_mm != null) { sa += Number(row.actual_mm); sn += Number(row.normal_mm); n++; }
      }
      setWindowPct(m);
      setWindowIn(mi);
      setWindowSummary(n ? { actualIn: sa / n / 25.4, normalIn: sn / n / 25.4, pct: sn > 0 ? sa / sn * 100 : null } : null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [metric, winDates]);
  // ── Map zoom/pan (scroll to zoom, drag to pan, double-click to reset) ──
  const [view, setView] = React.useState({ x: 0, y: 0, w: 800, h: 460 });
  const dragRef = React.useRef(null);
  const onMapWheel = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
    setView((v) => {
      const cx = v.x + px * v.w, cy = v.y + py * v.h;
      // Proportional to scroll amount → smooth on a trackpad, natural on a wheel,
      // and zooms toward the cursor (like Leaflet), not the center.
      const f = Math.pow(1.0015, e.deltaY);
      const nw = Math.max(100, Math.min(800, v.w * f)), nh = nw * (460 / 800);
      return { x: cx - px * nw, y: cy - py * nh, w: nw, h: nh };
    });
  }, []);
  // Bind wheel as a NON-passive native listener via a CALLBACK REF so it attaches
  // the instant the <svg> mounts (the map renders only after geo/data load, so a
  // plain useEffect-on-mount fired too early and never bound). preventDefault()
  // stops the page scrolling — React's synthetic onWheel is passive and can't.
  const wheelCleanupRef = React.useRef(null);
  const mapSvgRef = useCallback((el) => {
    if (wheelCleanupRef.current) { wheelCleanupRef.current(); wheelCleanupRef.current = null; }
    if (el) {
      const handler = (e) => { e.preventDefault(); onMapWheel(e); };
      el.addEventListener("wheel", handler, { passive: false });
      wheelCleanupRef.current = () => el.removeEventListener("wheel", handler);
    }
  }, [onMapWheel]);
  const onMapDown = useCallback((e) => { dragRef.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y, moved: false }; }, [view.x, view.y]);
  const onMapMove = useCallback((e) => {
    const d = dragRef.current; if (!d) return;
    const r = e.currentTarget.getBoundingClientRect();
    const dx = (e.clientX - d.sx) / r.width * view.w, dy = (e.clientY - d.sy) / r.height * view.h;
    if (Math.abs(e.clientX - d.sx) + Math.abs(e.clientY - d.sy) > 3) d.moved = true;
    setView((v) => ({ ...v, x: d.vx - dx, y: d.vy - dy }));
  }, [view.w, view.h]);
  const suppressClickRef = React.useRef(false);
  const onMapUp = useCallback(() => { if (dragRef.current && dragRef.current.moved) suppressClickRef.current = true; dragRef.current = null; }, []);
  const resetView = useCallback(() => setView({ x: 0, y: 0, w: 800, h: 460 }), []);
  const zoomed = view.w < 799;
  const [allGeoFeatures, setAllGeoFeatures] = React.useState(null);   // every model county's polygon; `geoData` narrows it to what's on screen
  const [d3Lib, setD3Lib] = React.useState(typeof window !== "undefined" ? window.d3 : null);
  // PRF rainfall grid overlay state
  const [showPrfGrids, setShowPrfGrids] = React.useState(false);
  const [prfInterval, setPrfInterval] = React.useState("MAYJUN");
  const [prfGrids, setPrfGrids] = React.useState([]);              // { grid_id, lat, lon }
  const [prfRainByGrid, setPrfRainByGrid] = React.useState({});    // grid_id → { interval_code → { rain, normal } }
  const [hoveredGrid, setHoveredGrid] = React.useState(null);      // grid_id of hovered/clicked grid
  // WNY weather grid overlay — the 389 Daymet cells. Default OFF so the metric
  // surface reads clean; toggle on for per-cell detail.
  const [showWnyGrid, setShowWnyGrid] = React.useState(false);
  const [wnyGridCells, setWnyGridCells] = React.useState([]);      // { lat, lon, county }
  // Customer-farm pin overlay — geocoded farmers with in-area corn APH. Default OFF.
  const [showFarms, setShowFarms] = React.useState(false);
  const [farms, setFarms] = React.useState([]);                    // { id, name, lat, lon, counties[] }
  // Town (civil township) outline overlay — Census TIGER, 338 WNY towns. Default
  // OFF. Lazy-loaded on first toggle. Outlines only; the town NAME shows on hover
  // (computed in onSvgMouseMove via geoContains) so the map never gets text-busy.
  const [showTowns, setShowTowns] = React.useState(false);
  const [townFeatures, setTownFeatures] = React.useState(null);
  const [hoveredTown, setHoveredTown] = React.useState(null);      // { name, cx, cy } in SVG coords
  React.useEffect(() => {
    if (!showTowns || townFeatures) return;
    fetch("/wny-towns.json").then((r) => r.json())
      .then((topo) => { try { setTownFeatures(decodeTopo(topo, topo.objects.towns)); } catch (_) { setTownFeatures([]); } })
      .catch(() => setTownFeatures([]));
  }, [showTowns, townFeatures]);
  // Per-cell season anomalies (% of normal precip / GDD, etc.) for the choropleth surface.
  const [anomalies, setAnomalies] = React.useState([]);            // { lat, lon, precip_pct_normal, gdd_pct_normal, ... }
  // ── A FAILED GRID LOAD IS NOT AN ABSENCE OF DATA ──────────────────────────
  // Every cell layer resolves out of `cellRows`, which is built by joining the
  // per-cell ANOMALIES to the grid CELLS. Both fetches ended in `.catch(() => [])`,
  // so a transient failure — a dropped request, a cold PostgREST schema cache, a
  // 5xx — produced an empty join, every county hatched, and a legend reading
  // "no 4km grid-cell data here". That sentence is a claim about the DATA and it
  // was false: the rows exist (1,390 NY cells, all carrying corn acres).
  //
  // Reported 2026-09-05 as "market intelligence map is not working for any
  // layer... still", then "all layers working now" twenty minutes later with
  // nothing deployed in between. An intermittent failure that renders as a
  // permanent data gap is the worst version of this: it reads as "we do not
  // have this", so nobody reports it as a bug and nobody retries.
  //
  // The state is now RECORDED and RENDERED, which is the whole fix. "loading"
  // and "failed" are different from "loaded, and genuinely empty here".
  const [gridLoad, setGridLoad] = React.useState("loading");   // loading | ok | failed
  // Kept apart from `gridLoad` on purpose: that flag is true when EITHER read
  // fails, and this one names the anomalies read specifically, because it is the
  // one that supplies the IDW control points every per-cell surface is drawn from.
  const [anomaliesFailed, setAnomaliesFailed] = React.useState(false);
  // Bumping this re-runs the fetch effect. A retry that cannot actually re-fetch
  // is a button that does nothing, which is worse than no button.
  const [gridReload, setGridReload] = React.useState(0);
  const [forecastSeries, setForecastSeries] = React.useState({});  // "lat|lon" -> [{d:'YYYY-MM-DD', in}] forecast daily
  const [fcHorizon, setFcHorizon] = React.useState(14);            // forecast-rain look-ahead window (days)
  // Forecast rain summed to today+fcHorizon, recomputed when the horizon button changes (no refetch).
  const forecastRain = React.useMemo(() => {
    const cut = new Date(); cut.setHours(0, 0, 0, 0); cut.setDate(cut.getDate() + fcHorizon);
    const cutISO = cut.toISOString().slice(0, 10);
    const out = {};
    for (const k of Object.keys(forecastSeries)) { let s = 0; for (const x of forecastSeries[k]) if (x.d <= cutISO) s += x.in; out[k] = s; }
    return out;
  }, [forecastSeries, fcHorizon]);
  // County lat/lon centroids — IDW control points for county-level metrics.
  // ── THE MAP DRAWS THE COUNTY SET IT WAS HANDED, NOT A GLOBAL LIST ──────────
  //
  // `geoData` used to be built straight off MI_MODEL_COUNTIES, so it always drew
  // all 39 counties no matter what `counties` contained. Switching the region
  // filter to Western NY therefore narrowed the NUMBERS but not the MAP: 39
  // outlines with 16 of them filled, which reads as broken rather than as a
  // filter — the same complaint that got the 52-county page pulled.
  //
  // It also meant fitSize() always framed the whole state. Adding Orange
  // (41.2°N), St Lawrence (45.0°N) and Dutchess stretched the extent north-south
  // until the projected content occupied x 118→682 of an 800-wide viewBox: the
  // book's own ground rendered as a thumbnail between two empty margins, and
  // zooming in far enough to read Orleans pushed the east off-screen. That is
  // the Suffolk lesson from 2026-08-13 arriving from the other direction — there
  // the fix was to drop a county, here it is to let the frame follow the filter.
  //
  // Deriving both the outlines and the IDW control points from `counties` means
  // the projection REFRAMES on toggle: Western NY gets exactly the view it had
  // before the expansion. Empty `counties` (first paint) falls back to every
  // feature so the map never flashes blank while the model loads.
  //
  // ── AMENDED 2026-08-20: THE FRAME FOLLOWS THE REGION, NOT THE LAYER ────────
  // The paragraph above is still the rule for the REGION toggle, and Western NY
  // still measures exactly 16. What changed is that "All New York" now means the
  // whole state on every layer instead of the yield model's 39, because the
  // alternative — a county set that changes with the metric — reframes the map
  // on every dropdown change and makes the outline itself an assertion about
  // where corn grows. A county the active layer has no data for is DRAWN and
  // hatched (see the fill below), which is the honest version of leaving it out.
  const { geoData, countyCentroidsLL } = React.useMemo(() => {
    if (!allGeoFeatures) return { geoData: null, countyCentroidsLL: {} };
    const want = new Set((counties || []).map((c) => String(c.fips)));
    // `wny` is the book view and stays narrowed to the counties it was handed.
    // Every other region frames the state; first paint (no counties yet) too.
    const features = (want.size && region === "wny")
      ? allGeoFeatures.filter((f) => want.has(f.properties.fips))
      : allGeoFeatures;
    // KEYED BY FIPS, not by name. FIPS_TO_NAME_GLOBAL spells 36089 "St. Lawrence"
    // and the model spells it "ST LAWRENCE", so a name key silently dropped that
    // county out of the IDW control points — one real county missing from every
    // county-level surface, with nothing on screen to say so.
    const cents = {};
    if (d3Lib && d3Lib.geoCentroid) {
      for (const f of features) {
        const [lon, lat] = d3Lib.geoCentroid(f);
        cents[String(f.properties.fips)] = [lon, lat];
      }
    }
    return { geoData: { type: "FeatureCollection", features }, countyCentroidsLL: cents };
  }, [allGeoFeatures, counties, d3Lib, region]);
  React.useEffect(() => {
    Promise.all([
      d3Promise,
      fetch("https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json").then((r) => r.json()).catch(() => null),
      // PRF grids covering WNY (lat 42-43.6, lon -79.8 to -76.5). Each grid is 0.25°.
      sbFetch("prf_grid_locations", { select: "grid_id,center_lat,center_lon", filters: ["center_lat=gte.42.0", "center_lat=lte.43.6", "center_lon=gte.-79.8", "center_lon=lte.-76.5"] }).catch(() => []),
      // Current-year per-grid rainfall (all intervals; UI picks which to show)
      sbFetch("prf_grid_indices", { select: "grid_id,interval_code,estimated_rainfall_inches,normal_rainfall_inches", filters: [`crop_year=eq.${new Date().getFullYear()}`] }).catch(() => []),
      // WNY weather grid cells for the grid-preview overlay + CDL corn weighting
      // (id needed to join the per-cell forecast precip below).
      sbFetch("wny_grid_cells", { select: "id,lat,lon,county_name,county_fips,corn_acres,nccpi_corn,awc", filters: [MI_GRID_STATE_FILTER] }).catch(() => null),
      // ⚠⚠ NOT IN THE PORTAL. Market Intelligence is classed `open` in PORTAL_PAGES, which
      // means "reads NO farmer data" and is grantable to somebody with no farm grants at all
      // — a staff member, a prospect. These two reads are farmer data: 207 customer farms
      // carry a lat/lon and this one plots them BY NAME.
      //
      // RLS is holding today and that is not the point. Measured 2026-10-03 through
      // rls_selftest: `farmers [owner]` 8 visible all own, `farmers [stranger]` 0 of the
      // owner's rows — so a portal user sees only their own farm and Kevin Rauber's preview
      // correctly read "Customer farms (0)". But this file's own rule is that A SERVER-SIDE
      // SCOPE IS NEVER THE ONLY GUARD ON A PORTAL SURFACE, and until this change RLS was
      // exactly that: one policy change, one service-role proxy, and 207 growers' farm
      // locations are on a prospect's screen.
      //
      // ⚠ AND THE DOCUMENTED JUSTIFICATION WAS WEAKER THAN THE CLAIM IT SUPPORTED. The
      // `open` class note said MarketIntelligenceView "takes no farmer PROPS at all", which
      // is true and beside the point — the map fetches farmers itself. Taking no farmer prop
      // is not reading no farmer data, and that note has been corrected.
      isPortal ? Promise.resolve([]) : sbFetch("farmers", { select: "id,name,lat,lon", filters: ["lat=not.is.null"] }).catch(() => []),
      // Their corn APH presence — used to filter to in-area corn farms only, so it is farmer
      // data too and goes the same way. Nothing downstream needs it once `farms` is empty.
      isPortal ? Promise.resolve([]) : sbFetch("production_history", { select: "farmer_id,county", filters: ["state=eq.NY", "crop=eq.CORN", "yield_per_acre=gt.0"] }).catch(() => []),
      // Per-cell season anomalies (current vs 30-yr normal) for the choropleth surface.
      // ny_ VIEW, AND THIS IS THE READ WHERE IT MATTERS MOST: these rows ARE the
      // IDW control points the choropleth is fit on. Unscoped, Pennsylvania's
      // cells become control points and bleed PA weather over the Southern Tier —
      // a number drawn on ground it was never fit on, which is exactly what the
      // no-data hatching beside this map exists to prevent.
      //
      // ⚠ BUG-055 — THIS READ WAS FIRED SIX TIMES AT ONCE, AND EACH ONE RECOMPUTED
      // THE WHOLE VIEW. The view returns 1,390 rows, over one 1,000-row page, so
      // _sbFetchAllPages opened its first batch at SB_CONCURRENCY and requested
      // pages 0-5 in parallel. A VIEW HAS NO MATERIALISATION: every one of those
      // Range requests re-ran both season aggregations from scratch, including the
      // four speculative pages past the end of a 1,390-row result. Measured
      // 2026-09-11 with EXPLAIN ANALYZE: 1,278 ms warm and 5,614 ms cold PER RUN,
      // against the `authenticated` statement_timeout of 8 s. Six of them
      // contending for the same buffers is what crossed it and returned 500.
      //
      // `maxPages: 2` sizes the first batch to the two pages 1,390 rows actually
      // need — six executions become two, with no global change to how any other
      // table is paged. NY's cell count is a fixed 0.1-degree lattice, and the
      // existing loud-truncation guard in _sbFetchAllPages still fires if it ever
      // outgrows 2,000.
      //
      // `order: "cell_id"` because Range paging needs a UNIQUE order, and this read
      // had none. Two page executions of a GROUP BY view with no ORDER BY are two
      // independent result sets; nothing guaranteed page 1 continued page 0 rather
      // than repeating or skipping cells at the seam — which would put duplicate or
      // missing IDW control points under the choropleth.
      //
      // The failure is already surfaced (the "Couldn't load the 4 km grid" notice,
      // #30), so this changes how often it fails, not how a failure reads.
      sbFetch("ny_grid_season_anomalies", { select: "lat,lon,precip_pct_normal,gdd_pct_normal,precip_curr_mm,gdd_curr,heat_days_curr,freeze_days_curr", filters: [], order: "cell_id", maxPages: 2 }).catch(() => null),
      // Forecast precip (Open-Meteo, today→~14d) per cell — summed for the "forecast rain" layer.
      sbFetch("ny_grid_weather_daily", { select: "cell_id,precip_mm,obs_date", filters: ["is_forecast=eq.true", `obs_date=gte.${new Date().toISOString().slice(0, 10)}`] }).catch(() => []),
    ]).catch(() => { setGridLoad("failed"); return []; }).then(([d3, topo, grids, idx, wnyCells, geocodedFarmers, cornHist, anomalyRows, fcDaily] = []) => {
      if (d3) setD3Lib(d3);
      if (topo) {
        // EVERY New York county except the five NYC boroughs — not just the ones
        // the yield model covers. Narrowing here to MI_MODEL_COUNTIES made the
        // 39-county yield gate silently decide the shape of the STATE, on layers
        // that have nothing to do with the yield model. Which of these get a
        // VALUE is decided per layer in the geoData memo above; which get drawn
        // is now the whole state, so the outline never changes with the metric.
        setAllGeoFeatures(decodeTopo(topo, topo.objects.counties)
          .filter((f) => String(f.properties.fips).startsWith("36") && !MI_NYC_FIPS.has(String(f.properties.fips))));
      }
      // Build PRF lookups — only keep grids that actually overlap our counties
      const wnyGrids = (grids || []).map((g) => ({ grid_id: g.grid_id, lat: Number(g.center_lat), lon: Number(g.center_lon) }));
      setPrfGrids(wnyGrids);
      const byGrid = {};
      for (const r of (idx || [])) {
        const gid = r.grid_id;
        if (!byGrid[gid]) byGrid[gid] = {};
        byGrid[gid][r.interval_code] = {
          rain: r.estimated_rainfall_inches != null ? Number(r.estimated_rainfall_inches) : null,
          normal: r.normal_rainfall_inches != null ? Number(r.normal_rainfall_inches) : null,
        };
      }
      setPrfRainByGrid(byGrid);
      // NULL means the request FAILED; [] would mean it succeeded and the grid is
      // genuinely empty. Those are different facts and the map now says which.
      // Either read failing leaves every cell layer with nothing to draw, so both
      // decide the state.
      setGridLoad((wnyCells && anomalyRows) ? "ok" : "failed");
      // ⚠ A FAILED READ MUST NOT WEAR THE SHAPE OF AN EMPTY ONE.
      // `ny_grid_season_anomalies` supplies EVERY per-cell surface its IDW
      // control points, so losing it draws no surface at all. The read is
      // `.catch(() => null)` precisely so a 500 is distinguishable from a grid
      // with nothing in it — `[]` would make the two identical and the map went
      // blank with no hatch and no legend. Recorded here so the renderer can say
      // WHICH of the two it is drawing.
      setAnomaliesFailed(anomalyRows == null);
      setWnyGridCells((wnyCells || []).map((c) => ({ lat: Number(c.lat), lon: Number(c.lon), county: c.county_name, county_fips: c.county_fips, corn_acres: c.corn_acres != null ? Number(c.corn_acres) : null, nccpi_corn: c.nccpi_corn != null ? Number(c.nccpi_corn) : null, awc: c.awc != null ? Number(c.awc) : null })));
      // Forecast rain: keep the per-cell daily forecast series (lat|lon → [{d,in}]) so the
      // forecast-horizon buttons (3/5/7/10/14 days) can re-sum without refetching.
      const idToLL = {};
      for (const c of (wnyCells || [])) if (c.id != null && c.lat != null && c.lon != null) idToLL[c.id] = `${Number(c.lat).toFixed(2)}|${Number(c.lon).toFixed(2)}`;
      const series = {};
      for (const r of (fcDaily || [])) { const k = idToLL[r.cell_id]; if (!k || r.precip_mm == null || !r.obs_date) continue; (series[k] = series[k] || []).push({ d: r.obs_date, in: Number(r.precip_mm) / 25.4 }); }
      setForecastSeries(series);
      // Filter geocoded farmers to those with corn APH in the 16 MI-WNY counties.
      const miWnyNames = new Set(MI_MODEL_COUNTIES.map((c) => c.name));
      const cornByFarmer = {};
      for (const r of (cornHist || [])) {
        const co = String(r.county || "").toUpperCase().trim();
        if (miWnyNames.has(co)) {
          if (!cornByFarmer[r.farmer_id]) cornByFarmer[r.farmer_id] = new Set();
          cornByFarmer[r.farmer_id].add(co);
        }
      }
      const inAreaFarms = (geocodedFarmers || [])
        .filter((f) => cornByFarmer[f.id])
        .map((f) => ({
          id: f.id, name: f.name,
          lat: Number(f.lat), lon: Number(f.lon),
          counties: Array.from(cornByFarmer[f.id]).sort(),
        }));
      setFarms(inAreaFarms);
      setAnomalies((anomalyRows || []).map((a) => ({
        lat: Number(a.lat), lon: Number(a.lon),
        precip_pct_normal: a.precip_pct_normal != null ? Number(a.precip_pct_normal) : null,
        gdd_pct_normal: a.gdd_pct_normal != null ? Number(a.gdd_pct_normal) : null,
        precip_curr_mm: a.precip_curr_mm != null ? Number(a.precip_curr_mm) : null,
        gdd_curr: a.gdd_curr != null ? Number(a.gdd_curr) : null,
        heat_days_curr: a.heat_days_curr != null ? Number(a.heat_days_curr) : null,
        freeze_days_curr: a.freeze_days_curr != null ? Number(a.freeze_days_curr) : null,
      })));
    });
  }, [gridReload]);
  // Index counties by upper-cased name for lookup against TopoJSON
  const byName = React.useMemo(() => {
    const out = {};
    for (const c of counties || []) {
      if (c.error) continue;
      out[String(c.name).toUpperCase()] = c;
    }
    return out;
  }, [counties]);
  // ...and by FIPS, which is what the map itself should join on. The TopoJSON's
  // county names come from FIPS_TO_NAME_GLOBAL and do NOT always match the
  // model's spelling ("St. Lawrence" vs "ST LAWRENCE"), so every name-keyed
  // lookup in the render silently returned nothing for that county. FIPS is the
  // key both sides actually agree on.
  const byFips = React.useMemo(() => {
    const out = {};
    for (const c of counties || []) {
      if (c.error) continue;
      out[String(c.fips)] = c;
    }
    return out;
  }, [counties]);
  // Per-county CDL coverage (loaded corn cells / total). Below ~70% the high-def
  // corn/production/yield surfaces would bleed misleading interpolation across the
  // gap (e.g. corn over Rochester), so those counties get masked as "no data".
  const cdlCoverageByFips = React.useMemo(() => {
    const tot = {}, loaded = {};
    for (const c of wnyGridCells) {
      const f = c.county_fips; if (!f) continue;
      tot[f] = (tot[f] || 0) + 1; if (c.corn_acres != null) loaded[f] = (loaded[f] || 0) + 1;
    }
    const out = {};
    for (const f of Object.keys(tot)) out[f] = (loaded[f] || 0) / tot[f];
    return out;
  }, [wnyGridCells]);
  // Adaptive range for the rainfall-INCHES maps: stretch the color ramp across the
  // current window's actual values (trimmed p4–p96) so a 3-day window and a season
  // total both get full gradient contrast instead of saturating one band.
  // anchorZero=true pins the low end at 0 so the palest color = ~no rain — truthful
  // for a FORECAST map (a pale cell genuinely means little/none, not "0.77+ but looks
  // empty"). anchorZero=false trims to p4 for max contrast when every cell is high
  // (season-length observed totals).
  const rainRange = (obj, anchorZero) => {
    const vals = Object.values(obj || {}).filter((v) => v != null && !isNaN(v)).sort((a, b) => a - b);
    if (vals.length < 3) return { lo: 0, hi: 1, thr: [0.1, 0.25, 0.5, 1] };
    const q = (p) => vals[Math.max(0, Math.min(vals.length - 1, Math.floor((vals.length - 1) * p)))];
    let lo = anchorZero ? 0 : Math.max(0, q(0.04)), hi = q(0.96);
    if (hi - lo < 0.15) hi = lo + 0.15;
    lo = Math.floor(lo * 100) / 100; hi = Math.ceil(hi * 100) / 100;
    const thr = Array.from({ length: 7 }, (_, i) => Math.round((lo + (hi - lo) * (i + 1) / 8) * 100) / 100);
    return { lo, hi, thr };
  };
  const inchesRange = React.useMemo(() => rainRange(windowIn, false), [windowIn]);   // observed: adaptive floor (contrast)
  // Adaptive Season-GDD range — cumulative GDD climbs all season, so any fixed
  // ceiling eventually clamps every county to one shade. Stretch the ramp across
  // the current cells' p4–p96 so the gradient always shows real spread.
  const gddRange = React.useMemo(() => {
    const vals = (anomalies || []).map((a) => a.gdd_curr).filter((v) => v != null && !isNaN(v)).sort((a, b) => a - b);
    if (vals.length < 3) return { lo: 150, hi: 450, thr: [200, 250, 300, 350, 400] };
    const q = (p) => vals[Math.max(0, Math.min(vals.length - 1, Math.floor((vals.length - 1) * p)))];
    let lo = q(0.04), hi = q(0.96);
    if (hi - lo < 40) hi = lo + 40;
    lo = Math.floor(lo / 10) * 10; hi = Math.ceil(hi / 10) * 10;
    const thr = Array.from({ length: 6 }, (_, i) => Math.round(lo + (hi - lo) * (i + 1) / 7));
    return { lo, hi, thr };
  }, [anomalies]);
  // Metric definitions: label, accessor, color scale, format
  const METRICS = React.useMemo(() => {
   // Per-county lookups for the HIGH-DEF yield-%-of-normal map (TRUE this-year
   // anomaly): county forecast yield, N-yr normal yield, and the county's mean
   // soil-water factor. The per-cell value cancels the static soil baseline and
   // keeps only this year's per-cell dry-down deviation — see the accessor.
   const normalYieldByFips = {}, fcYieldByFips = {}, countyWaterByFips = {};
   for (const c of (counties || [])) {
     if (c.error || !c.fips) continue;
     const s = (c.yld_series || []).filter((x) => x.y > 0).slice(-normalYears);
     if (s.length) normalYieldByFips[c.fips] = s.reduce((a, b) => a + b.y, 0) / s.length;
     if (c.forecast_yield != null) fcYieldByFips[c.fips] = c.forecast_yield;
     if (c.water_balance && c.water_balance.factor != null) countyWaterByFips[c.fips] = c.water_balance.factor;
   }
   // ── Elevator supply-lens accessors: historical yield (RMA final 2023-25 →
   //    NASS ≤2022 fallback) + production (yield × NASS grain harvested acres,
   //    silage excluded), averaged over the selected comparison year(s). ──
   const histYieldFor = (cName, yr) => { const r = histRows && histRows[String(cName).toUpperCase()]; if (!r) return null; const v = (r.rma[yr] != null ? r.rma[yr] : r.nass[yr]); return v != null ? v : null; };
   // Grain harvested acres for the year — NASS suppresses acreage for some
   // county-years, so fall back to the nearest available year (acreage is stable
   // YoY; the volatile part is yield). Keeps production maps from going blank.
   const grainAcFor = (r, yr) => { if (r.grainAc[yr] != null) return r.grainAc[yr]; let best = null, bd = 1e9; for (const k of Object.keys(r.grainAc)) { const d = Math.abs(+k - yr); if (d < bd) { bd = d; best = r.grainAc[k]; } } return best; };
   const histProdFor = (cName, yr) => { const r = histRows && histRows[String(cName).toUpperCase()]; if (!r) return null; const y = histYieldFor(cName, yr), ac = grainAcFor(r, yr); return (y != null && ac != null) ? y * ac : null; };
   const cmpAvg = (cName, fn) => { const xs = (cmpYears || []).map((y) => fn(cName, y)).filter((v) => v != null); return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null; };
   const cmpLbl = (cmpYears || []).join("/");
   return ({
    // ── HIGH-DEF per-cell metrics (338 gridMET control points; Jensen-safe) ──
    precip_pct_normal: { label: "Season rainfall — % of normal", fmt: (v) => `${v?.toFixed(0)}%`, cellAccessor: (a) => a.precip_pct_normal, scale: "diverging_precip_pct", domain: [50, 175], thresholds: [60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160] },
    precip_pct_window: { label: "Rainfall — % of normal (pick dates)", fmt: (v) => `${v?.toFixed(0)}%`, cellAccessor: (a) => a.precip_pct_window, scale: "diverging_precip_pct", domain: [50, 175], thresholds: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160] },
    forecast_rain:     { label: "Forecast rain — days ahead (in)", fmt: (v) => (v == null ? "—" : `${v.toFixed(2)}"`), cellAccessor: (a) => a.forecast_rain, scale: "rain_green", domain: [0, 3.5], thresholds: [0.1, 0.25, 0.5, 1, 2, 3.5] },
    gdd_pct_normal:    { label: "Season GDD — % of normal", fmt: (v) => `${v?.toFixed(0)}%`, cellAccessor: (a) => a.gdd_pct_normal, scale: "diverging_gdd_pct", domain: [80, 120], thresholds: [85, 92, 97, 100, 103, 108, 115] },
    precip_in_window:  { label: "Rainfall (in) — pick dates (~4 km)", fmt: (v) => (v == null ? "—" : `${v.toFixed(2)}"`), cellAccessor: (a) => a.precip_in_window, scale: "rain_adaptive", domain: [inchesRange.lo, inchesRange.hi], thresholds: inchesRange.thr },
    season_gdd_grid:   { label: "Season GDD — high-def", fmt: (v) => v?.toFixed(0), cellAccessor: (a) => a.gdd_curr, scale: "red_high", domain: [gddRange.lo, gddRange.hi], thresholds: gddRange.thr },
    heat_days_grid:    { label: "Heat days (Tmax>90°F)", fmt: (v) => `${Math.round(v)}`, cellAccessor: (a) => a.heat_days_curr, scale: "red_high", domain: [0, 8], thresholds: [1, 2, 3, 5, 7] },
    freeze_days_grid:  { label: "Freeze days (Tmin≤28°F)", fmt: (v) => `${Math.round(v)}`, cellAccessor: (a) => a.freeze_days_curr, scale: "blue_high", domain: [0, 8], thresholds: [1, 2, 3, 5, 7] },
    // ── RAIN (MRMS) — OUR native ~1 km gauge-corrected rainfall, summed over 1/2/3-day
    //    windows, client-rendered as an image overlay from the raingrid endpoint (exact
    //    grib values, consistent with the per-field data — replaced the coarser/binned IEM
    //    WMS). Not a per-cell metric (no cellAccessor → no choropleth); isRadar flips county
    //    fills transparent + swaps the legend + reads the value under the cursor via the LUT. ──
    radar_p72h: { isRadar: true, radar: "mrms_p72h", label: "Rain — last 72 h (exact 1 km, fresh)", scale: "rain_adaptive", domain: [0, 1], fmt: () => "" },
    radar_p48h: { isRadar: true, radar: "mrms_p48h", label: "Rain — last 48 h (exact 1 km, fresh)", scale: "rain_adaptive", domain: [0, 1], fmt: () => "" },
    radar_p24h: { isRadar: true, radar: "mrms_p24h", label: "Rain — last 24 h (exact 1 km, fresh)", scale: "rain_adaptive", domain: [0, 1], fmt: () => "" },
    // ── CDL corn-aware high-def surfaces (per-cell, masked to where corn grows) ──
    yield_hd:      { muteCorn: true, label: "Predicted yield (bu/ac, high-def — sub-county by soil)", fmt: (v) => `${v?.toFixed(0)} bu/ac`, cellAccessor: (a) => a.cell_yield, scale: "green_high", domain: [120, 200], thresholds: [135, 148, 160, 172, 184, 194] },
    corn_density:  { needsCorn: true, muteCorn: true, label: "Corn density (USDA CDL corn ac per map cell)", fmt: (v) => `${Math.round(v).toLocaleString()} ac corn`,
      // Each cell is 0.1°×0.1° (~35 mi² ≈ 22k ac of land at WNY latitude). Show the
      // corn acres as a share of that tile so the raw number reads as a density.
      hoverSub: (v, cell) => { if (!cell || cell.lat == null) return null; const ac = 123.86 * Math.cos(cell.lat * Math.PI / 180) * 247.105; if (!ac) return null; return `≈${Math.round(v / ac * 100)}% corn cover · ~${Math.round(ac / 1000)}k-ac cell`; },
      cellAccessor: (a) => a.corn_acres, scale: "green_high", domain: [0, 5000], thresholds: [250, 1000, 2000, 3500, 4800] },
    soil_nccpi:    { label: "Soil productivity (NCCPI, USDA SSURGO — long-run avg)", fmt: (v) => v?.toFixed(3), cellAccessor: (a) => a.nccpi_corn, scale: "soil_div", domain: [0.24, 0.56], thresholds: [0.27, 0.31, 0.35, 0.40, 0.45, 0.50] },
    soil_awc:      { label: "Soil water capacity (AWS 0-150cm, SSURGO — low = gravelly/droughty)", fmt: (v) => `${v?.toFixed(1)} cm`, cellAccessor: (a) => a.awc, scale: "soil_div", domain: [5, 30], thresholds: [8, 11, 14, 18, 22, 26] },
    soil_water_stress: { muteCorn: true, label: "Soil-water dry-down (this-year stress, FAO-56 bucket — soil×weather)", fmt: (v) => (v == null ? "—" : v <= 0 ? `+${(-v).toFixed(1)}%` : `−${v.toFixed(1)}%`), cellAccessor: (a) => a.water_drag_pct, scale: "drydown", domain: [0, 15], thresholds: [-10, 0, 1.5, 3.5, 6, 10, 15] },
    // Scouting layer — where conditions have FAVORED foliar disease. Carries no
    // yield effect by design (see miDiseasePressure); it tells you where to walk
    // fields, not what the crop will make.
    disease_pressure: { muteCorn: true, label: "Disease pressure (favorable days, last 30 — scouting only, no yield effect)", fmt: (v) => (v == null ? "—" : `${Math.round(v)}%`), cellAccessor: (a) => a.disease_score, scale: "disease", domain: [0, 100], thresholds: [25, 35, 45, 55, 65],
      hoverSub: (v, cell) => {
        const d = cell && cell.disease;
        if (!d) return null;
        return `${d.favorable_days}/${d.days} d favorable · tar spot ${d.tar_spot} · GLS/NCLB ${d.gls_nclb} · white mold ${d.white_mold} · ${d.wet_streak}d wet streak · ${d.rain_in}"`;
      } },
    production_hd: { needsCorn: true, muteCorn: true, label: "Production — high-def (corn-grain bu per map cell)", fmt: (v) => v >= 1e6 ? `${(v / 1e6).toFixed(2)}M bu` : `${(v / 1000).toFixed(0)}k bu`,
      // Per-cell total grain bushels (county forecast distributed by corn×soil), over
      // the same ~22k-ac tile. Show implied bu/ac so the per-cell total reads in context.
      // Don't divide grain bu by TOTAL corn acres (that includes silage acres,
      // which yield no grain — it understates bu/ac badly). Use the cell's own
      // predicted yield (same value as the yield map) and back into GRAIN acres.
      hoverSub: (v, cell) => { if (!cell) return null; const y = cell.cell_yield; if (y && y > 0) return `≈${Math.round(y)} bu/ac × ~${Math.round(v / y).toLocaleString()} ac grain corn`; return null; },
      cellAccessor: (a) => a.cell_prod, scale: "green_high", domain: [0, 800000], thresholds: [90000, 180000, 270000, 360000, 450000, 540000, 630000, 720000] },
    // ── COUNTY-LEVEL metrics (16 county-centroid control points → IDW surface) ──
    forecast_yield: { label: "Predicted yield (bu/ac)", fmt: (v) => `${v?.toFixed(1)} bu/ac`, getter: (c) => c.forecast_yield, scale: "green_high", domain: [120, 200], thresholds: [130, 145, 160, 175, 190] },
    pred_prod:      { label: "Predicted production (bu)", fmt: (v) => `${(v / 1e6).toFixed(2)}M bu`, getter: (c) => c.pred_prod, scale: "green_high", domain: [0, 4e6], thresholds: [0.5e6, 1e6, 2e6, 3e6] },
    // ── ELEVATOR supply lens: historical yield/production by selected year(s),
    //    + the current-year projection as % of those selected year(s). Yield =
    //    RMA final (2023-25) with NASS fallback (≤2022); production = yield ×
    //    NASS grain harvested acres (silage excluded). ──
    hist_yield: { label: `Historical yield — ${cmpLbl} avg (RMA final → NASS)`, fmt: (v) => (v == null ? "—" : `${v.toFixed(1)} bu/ac`), flatCounty: true, adaptive: "seq",
      getter: (c) => cmpAvg(c.name, histYieldFor), scale: "green_high", domain: [120, 200], thresholds: [130, 145, 160, 175, 190] },
    hist_prod: { label: `Historical production — ${cmpLbl} avg (yld × harv ac, no silage)`, fmt: (v) => (v == null ? "—" : `${(v / 1e6).toFixed(2)}M bu`), flatCounty: true, adaptive: "seq",
      getter: (c) => cmpAvg(c.name, histProdFor), scale: "green_high", domain: [0, 6e6], thresholds: [0.5e6, 1e6, 2e6, 3e6, 4e6, 5e6] },
    yield_vs_cmp: { label: `2026 yield vs ${cmpLbl} (% of selected)`, fmt: (v) => (v == null ? "—" : `${Math.round(v)}%`), flatCounty: true, adaptive: "ctr100",
      getter: (c) => { const a = cmpAvg(c.name, histYieldFor); return (a > 0 && c.forecast_yield != null) ? c.forecast_yield / a * 100 : null; },
      scale: "pct_div_adaptive", domain: [60, 140], thresholds: [75, 85, 92, 98, 104, 112, 122] },
    prod_vs_cmp: { label: `2026 production vs ${cmpLbl} (% of selected)`, fmt: (v) => (v == null ? "—" : `${Math.round(v)}%`), flatCounty: true, adaptive: "ctr100",
      getter: (c) => { const a = cmpAvg(c.name, histProdFor); return (a > 0 && c.pred_prod != null) ? c.pred_prod / a * 100 : null; },
      scale: "pct_div_adaptive", domain: [60, 140], thresholds: [75, 85, 92, 98, 104, 112, 122] },
    production_pct_normal: { label: `Predicted production — % of normal (${normalYears}-yr)`, flatCounty: true, adaptive: "ctr100", fmt: (v) => (v == null ? "—" : `${Math.round(v)}%`),
      getter: (c) => {
        const h = c.prod_hist; if (!h || !h.length || c.pred_prod == null) return null;
        const recent = h.filter((x) => x.bu > 0).slice(-normalYears);
        if (!recent.length) return null;
        const normal = recent.reduce((a, b) => a + b.bu, 0) / recent.length;
        return normal > 0 ? c.pred_prod / normal * 100 : null;
      },
      scale: "pct_div_adaptive", domain: [60, 140], thresholds: [75, 85, 92, 98, 104, 112, 122] },
    yield_pct_normal_hd: { muteCorn: true, label: `Yield — % of normal (high-def, this-year, ${normalYears}-yr)`, fmt: (v) => (v == null ? "—" : `${Math.round(v)}%`),
      // TRUE anomaly: county %-of-normal × this cell's soil-water deviation from
      // the county mean. The soil baseline CANCELS (we don't multiply by it), so
      // a normal-weather cell reads ~county % regardless of soil — the sub-county
      // texture is THIS YEAR's dry-down, not static soil quality.
      cellAccessor: (a) => {
        const f = a.fips; if (!f) return null;
        const cf = fcYieldByFips[f], cn = normalYieldByFips[f];
        if (!(cf > 0) || !(cn > 0)) return null;
        let w = 1; const cw = a.water_factor, cwc = countyWaterByFips[f];
        if (cw != null && cwc > 0) w = cw / cwc;
        return cf / cn * w * 100;
      },
      scale: "pct_normal_green", domain: [60, 140], thresholds: [75, 85, 92, 98, 104, 112, 122] },
    precip_anom:    { label: "Rainfall anomaly (county model, vs normal)", fmt: (v) => `${v > 0 ? "+" : ""}${Math.round(v * 100)}%`, getter: (c) => c.weather?.precip_anom, scale: "diverging_precip", domain: [-0.5, 0.5], thresholds: [-0.3, -0.15, 0, 0.15, 0.3] },
    season_precip:  { label: "Season rainfall (county model, in)", fmt: (v) => `${v?.toFixed(1)}"`, getter: (c) => c.weather?.season_precip_in, scale: "blue_high", domain: [2, 7], thresholds: [3, 4, 5, 6] },
    gdd_anom:       { label: "GDD anomaly (county model, vs normal)", fmt: (v) => `${v > 0 ? "+" : ""}${Math.round(v * 100)}%`, getter: (c) => c.weather?.gdd_anom, scale: "diverging_gdd", domain: [-0.3, 0.3], thresholds: [-0.2, -0.1, 0, 0.1, 0.2] },
    season_gdd:     { label: "Season GDD (county model)", fmt: (v) => v?.toFixed(0), getter: (c) => c.weather?.season_gdd, scale: "red_high", domain: [50, 300], thresholds: [100, 150, 200, 250] },
    drought_dsci:   { label: "USDM drought (DSCI)", flatCounty: true, fmt: (v) => `DSCI ${v}`, getter: (c) => c.drought?.dsci, scale: "drought", domain: [0, 250], thresholds: [25, 50, 100, 150, 200] },
    soil_water_drag: { label: "Soil-water drag (county — measured; already inside Total wx)", fmt: (v) => `−${v?.toFixed(1)}%`, getter: (c) => (c.water_balance ? c.water_balance.drag_pct : c.early_stress?.drag_pct), scale: "red_high", domain: [0, 12], thresholds: [1, 2, 4, 6, 9] },
    total_wx:       { label: "Total weather factor", fmt: (v) => `×${v?.toFixed(3)}`, getter: (c) => c.total_wx_factor, scale: "green_high", domain: [0.85, 1.05], thresholds: [0.9, 0.95, 1.0, 1.02] },
    dairy_cows:     { label: "Dairy cow inventory (head)", flatCounty: true, adaptive: "seq", fmt: (v) => v != null ? v.toLocaleString() + " head" : "—", getter: (c) => c.dairy_cows, scale: "blue_high", domain: [0, 50000], thresholds: [5000, 15000, 25000, 40000] },
    silage_yield_est: { label: "Silage yield estimate (t/ac)", flatCounty: true, adaptive: "seq", fmt: (v) => v != null ? v.toFixed(1) + " t/ac" : "—", getter: (c) => c.silage?.yield_est_t_per_ac, scale: "green_high", domain: [12, 22], thresholds: [14, 16, 18, 20] },
    silage_extra:    { label: "Extra silage acres vs normal", flatCounty: true, adaptive: "seq", fmt: (v) => v != null ? v.toLocaleString() + " ac" : "—", getter: (c) => c.silage?.extra_acres_vs_normal, scale: "red_high", domain: [0, 6000], thresholds: [500, 1500, 3000, 4500] },
    silage_displaced_grain: { label: "Corn acres chopped for silage", flatCounty: true, adaptive: "seq", fmt: (v) => v != null ? v.toLocaleString() + " ac" : "—", getter: (c) => c.silage_displaced_grain_ac, scale: "red_high", domain: [0, 40000], thresholds: [3000, 8000, 14000, 21000, 28000, 35000] },
  }); }, [inchesRange, normalYears, counties, gddRange, histRows, cmpYears]);
  const M = METRICS[metric];
  // Does the ACTIVE layer resolve per grid cell (CDL / SSURGO / gridMET), or per
  // county out of the yield model? That is the only thing that decides which
  // counties can carry a value — see MI_CELL_DATA_LAYERS.
  const layerIsCellData = MI_CELL_DATA_LAYERS.has(metric);
  // ⚠ A GAP MUST NOT BE BLAMED ON A SOURCE THAT IS FINE.
  // `counties` comes from the yield model and the map does NOT wait for it —
  // `geoData` is built from the county boundary file, which lands first. So in
  // the window between the two, every county fails `layerHasData` and the
  // coverage line read "0 of 16 counties carry this layer · no county yield
  // model (USDA NASS publishes under 6 years of county corn grain yield)".
  //
  // That is false about all sixteen Western NY counties. It is the page
  // reporting its own LOADING STATE as a finding about NASS, and it is what a
  // reader is left holding when the model is slow — a past crop year has no
  // stored snapshot, so it is recomputed in the browser over ~425k weather
  // rows. Reported live on 2026-09-03 as "the yield prediction model is not
  // working": the right conclusion from the wrong evidence.
  //
  // So a per-county REASON may only be given once there IS a model to compare
  // against. Cell layers are excluded deliberately — corn density, soil and the
  // gridMET surfaces resolve per cell and need no county model, so claiming the
  // model is missing on those would be the same false explanation pointed the
  // other way.
  const modelHasCounties = React.useMemo(
    () => (counties || []).some((c) => !c.error), [counties]);
  const modelMissing = !layerIsCellData && !modelHasCounties;
  // These two are the only layers that read per-cell values off gridShadow. On
  // the snapshot fast path gridShadow is null, so ask for the stored layers the
  // moment one is selected — otherwise the map draws outlines and a "No data"
  // legend, which is what it did from 2026-08-13 until this was noticed.
  const NEEDS_CELL_LAYERS = React.useMemo(() => new Set(["soil_water_stress", "disease_pressure"]), []);
  React.useEffect(() => {
    if (NEEDS_CELL_LAYERS.has(metric) && typeof onNeedCellLayers === "function") onNeedCellLayers();
  }, [metric, onNeedCellLayers, NEEDS_CELL_LAYERS]);
  const cellLayersPending = NEEDS_CELL_LAYERS.has(metric) && (cellLayersState === "loading" || cellLayersState === "idle");
  const cellLayersMissing = NEEDS_CELL_LAYERS.has(metric) && cellLayersState === "empty";
  // ⚠ A LAYER WHOSE NUMBERS HAVE NOT ARRIVED IS NOT A LAYER WITH NO NUMBERS.
  //
  // Kevin, 2026-09-08: "Market Intelligence yield prediction maps are all broken
  // again.. why? You already fixed this once."
  //
  // Measured against production before changing anything: the model is healthy —
  // all 39 counties carry a forecast_yield, every browser read returns 200, all
  // 1,390 anomaly rows join to a cell and 958 resolve a county yield. Rendered at
  // --wait=25000 the map is a full green choropleth. Rendered at --wait=6000 it is
  // a complete, fully-labelled GREY map with a "No data" legend.
  //
  // The county outlines come from a CDN topojson and land first; the surface is an
  // IDW fit over cellRows, which waits on the ~1,390-row anomaly read (2.7 s on a
  // fast connection) and the d3 CDN. In between the page showed a confident empty
  // map — and for yield_hd said nothing at all, because the "still loading the 4km
  // grid…" line below is gated on layerIsCellData and the failed-grid banner is
  // too. yield_hd is not cell-data, so it got neither: 12 counties hatched with
  // "no county yield model" (true of those 12, and nothing to do with loading) and
  // 39 blank ones with no explanation whatsoever.
  //
  // ⚠ THE TEST IS THE LAYER’S OWN ACCESSOR, NOT MI_CELL_DATA_LAYERS. Those answer
  // different questions, and conflating them is how this comes back a third time.
  // MI_CELL_DATA_LAYERS decides which counties a layer may CLAIM — check-mi-layers
  // guards it, and yield_hd is deliberately excluded so a forecast yield is never
  // painted on a county with no yield history. Whether a layer needs cellRows to
  // DRAW is a property of its own accessor, and yield_hd — the layer the page
  // opens on — has one. Both registries are correct as they stand.
  //
  // ⚠ AND IT CANNOT HANG. `gridLoad` is the loader’s own tri-state and leaves
  // "loading" on success AND on failure, so a layer that is genuinely empty falls
  // through to the hatch-and-explain path instead of sitting on "Loading map…".
  // (the two flags this paragraph describes are declared just below the cellRows
  //  memo, which they read — see `cellSurfacePending` there.)
  // Adaptive color domain: for metrics flagged `adaptive`, stretch the color
  // domain to the ACTUAL county values so the ramp fills the real spread (fixes
  // the fixed-domain saturation where every county >122% read identical green).
  // "ctr100" keeps 100% as the neutral diverging midpoint; "seq" is a plain
  // min→max stretch for sequential ramps. Falls back to the static domain.
  const activeScale = React.useMemo(() => {
    const base = M ? { domain: M.domain, thresholds: M.thresholds } : { domain: [0, 1], thresholds: [] };
    if (!M || !M.adaptive || typeof M.getter !== "function") return base;
    const vals = (counties || []).map((c) => { try { return M.getter(c); } catch (_) { return null; } }).filter((v) => v != null && !isNaN(v)).map(Number).sort((a, b) => a - b);
    if (vals.length < 2) return base;
    let lo = vals[0], hi = vals[vals.length - 1];
    if (M.adaptive === "ctr100") { const half = Math.max(Math.abs(hi - 100), Math.abs(100 - lo), 6); lo = 100 - half; hi = 100 + half; }
    else { const pad = (hi - lo) * 0.04; lo -= pad; hi += pad; }
    if (hi - lo < 1e-9) hi = lo + 1;
    const thresholds = Array.from({ length: 7 }, (_, i) => lo + (hi - lo) * (i + 1) / 8);
    return { domain: [lo, hi], thresholds };
  }, [M, counties]);
  // 16-county rollup for the active elevator metric + selected year(s) — shown in
  // a summary box. Yields are acre-weighted (true regional yield); production is
  // summed. The % metrics count only counties with BOTH 2026 + historical so the
  // ratio is apples-to-apples.
  const regionSummary = React.useMemo(() => {
    if (!ELEV_METRICS.includes(metric) || !histRows) return null;
    const up = (s) => String(s).toUpperCase();
    const yF = (nm, yr) => { const r = histRows[up(nm)]; if (!r) return null; const v = r.rma[yr] != null ? r.rma[yr] : r.nass[yr]; return v != null ? v : null; };
    const acF = (nm, yr) => { const r = histRows[up(nm)]; if (!r) return null; if (r.grainAc[yr] != null) return r.grainAc[yr]; let b = null, bd = 1e9; for (const k of Object.keys(r.grainAc)) { const d = Math.abs(+k - yr); if (d < bd) { bd = d; b = r.grainAc[k]; } } return b; };
    const cAvg = (nm, fn) => { const xs = (cmpYears || []).map((y) => fn(nm, y)).filter((v) => v != null); return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null; };
    const pAvg = (nm) => cAvg(nm, (n, yr) => { const y = yF(n, yr), a = acF(n, yr); return (y != null && a != null) ? y * a : null; });
    const cs = (counties || []).filter((c) => !c.error && c.name);
    const cmpLbl = (cmpYears || []).join("/");
    const fmtM = (v) => `${(v / 1e6).toFixed(1)}M bu`;
    if (metric === "hist_yield" || metric === "yield_vs_cmp") {
      let wy = 0, wyc = 0, w = 0, wBoth = 0, wyBoth = 0, wycBoth = 0;
      for (const c of cs) {
        const a = cAvg(c.name, acF), hy = cAvg(c.name, yF); if (a == null) continue;
        w += a; if (hy != null) wy += hy * a;
        if (hy != null && c.forecast_yield != null) { wBoth += a; wyBoth += hy * a; wycBoth += c.forecast_yield * a; }
      }
      if (metric === "hist_yield") return w ? { txt: `${(wy / w).toFixed(1)} bu/ac`, sub: `16-county acre-wtd avg · ${cmpLbl}` } : null;
      if (!wBoth || !wyBoth) return null;
      const pct = (wycBoth / wyBoth) * 100;
      return { txt: `${Math.round(pct)}%`, pct, sub: `16-county 2026 yield ${(wycBoth / wBoth).toFixed(0)} vs ${cmpLbl} ${(wyBoth / wBoth).toFixed(0)} bu/ac` };
    }
    let cur = 0, base = 0, baseAll = 0;
    for (const c of cs) { const hp = pAvg(c.name); if (hp != null) baseAll += hp; if (hp != null && c.pred_prod != null) { base += hp; cur += c.pred_prod; } }
    if (metric === "hist_prod") return baseAll ? { txt: fmtM(baseAll), sub: `16-county total · ${cmpLbl}` } : null;
    if (!base) return null;
    const pct = cur / base * 100;
    return { txt: `${Math.round(pct)}%`, pct, sub: `16-county 2026 ${fmtM(cur)} vs ${cmpLbl} ${fmtM(base)}` };
  }, [metric, histRows, cmpYears, counties]);
  // Color scale helpers
  function scaleColor(scaleName, v, domain) {
    if (v == null || isNaN(v)) return "#f2f2f0";
    const [lo, hi] = domain;
    const t = Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
    if (scaleName === "green_high") {
      // 9-stop light→dark green ramp — finer gradient so high-def surfaces
      // (yield, production, corn density) render smooth detail, not coarse bands.
      const palette = ["#edf6ed", "#d6ebd4", "#bce0b6", "#9ed197", "#7cc077", "#58aa52", "#3a9038", "#277528", "#15531c"];
      return palette[Math.min(palette.length - 1, Math.floor(t * palette.length))];
    }
    if (scaleName === "soil_div") {
      // poor soil = tan/brown → rich soil = deep green (high contrast for the gradient)
      const palette = ["#a9772f", "#cdab6d", "#e6d9a8", "#a9c97e", "#5a9e4f", "#2d6a2d"];
      return palette[Math.min(palette.length - 1, Math.floor(t * palette.length))];
    }
    if (scaleName === "red_high") {
      // 7-stop light→dark red ramp — finer gradient (GDD, silage, heat days).
      const palette = ["#fdeae0", "#f8cdb0", "#f0a578", "#e57a4a", "#cf4f2c", "#a83018", "#7a1c08"];
      return palette[Math.min(palette.length - 1, Math.floor(t * palette.length))];
    }
    if (scaleName === "drydown") {
      // Soil-water dry-down (% yield drag). ~0 = healthy GREEN; ramps lime→yellow→
      // orange→deep red as the cell's water bank draws below threshold. High
      // contrast at SMALL values so an emerging dry-down is visible early — and
      // "fine" reads as green, not near-white. Threshold-based (absolute %).
      if (v <= 0)   return "#2e7d32";   // beneficial / no stress
      if (v < 1.5)  return "#7cb342";   // negligible
      if (v < 3.5)  return "#cddc39";   // mild (lime)
      if (v < 6)    return "#f1c40f";   // moderate (yellow)
      if (v < 10)   return "#e8731c";   // significant (orange)
      if (v < 15)   return "#c0392b";   // severe (red)
      return "#7a1500";                 // extreme (deep red)
    }
    if (scaleName === "disease") {
      // % of the last 30 days favorable for corn foliar disease. Deliberately NOT
      // a green→red "good/bad" ramp — this is risk, not yield, and green here
      // would read as "good corn". Neutral straw → purple, which nothing else on
      // this map uses, so the layer can't be mistaken for a yield surface.
      // Bands are ABSOLUTE (not stretched to the current year) so a quiet season
      // reads pale and a bad one reads dark, and set from what the index actually
      // occupies — per-cell distribution over 2021-2026, all 338 cells, 30 days
      // ending Aug 8: full range 0-80, medians 2021:47 2022:43 2023:57 2024:50
      // 2025:23 2026:37. That ordering is itself the check that the index behaves —
      // 2025 (drought) lands lowest and 2023 (very wet) highest, unprompted.
      if (v < 25) return "#f2efe4";   // quiet — 2025/drought-like
      if (v < 35) return "#ddd0e0";
      if (v < 45) return "#c3a8cf";   // typical NY summer
      if (v < 55) return "#a077b6";   // elevated
      if (v < 65) return "#7a4b95";   // high
      return "#54246e";               // sustained favorable — go scout
    }
    if (scaleName === "blue_high") {
      const palette = ["#e0eaf2", "#b8d0e6", "#7ab0d6", "#4a8eb8", "#1a5a8a"];
      return palette[Math.min(palette.length - 1, Math.floor(t * palette.length))];
    }
    if (scaleName === "rain_adaptive") {
      // 8-stop green ramp stretched across the window's adaptive domain (t already
      // normalized to [lo,hi]) — full gradient contrast at any window length.
      const palette = ["#eef6e9", "#d4ebc6", "#b1dc9c", "#86c46b", "#5bab4c", "#358f3d", "#1f6e2f", "#0c4d1e"];
      return palette[Math.min(palette.length - 1, Math.floor(t * palette.length))];
    }
    if (scaleName === "rain_green") {
      // Rainfall AMOUNT (inches) — threshold-based so it resolves both short windows
      // (a few tenths") and season totals (multiple inches). Pale = dry → deep green = wet.
      if (v < 0.1) return "#eef3e9";
      if (v < 0.25) return "#d8ead0";
      if (v < 0.5) return "#b5d99f";
      if (v < 1) return "#86c46b";
      if (v < 2) return "#4ea64a";
      if (v < 3.5) return "#2d8a3e";
      return "#16632a";
    }
    if (scaleName === "drought") {
      // 0=green, 50=yellow, 100=orange, 200+ red
      if (v >= 200) return "#8b1c0c";
      if (v >= 100) return "#c04030";
      if (v >= 50)  return "#e08060";
      if (v >= 25)  return "#f0c878";
      if (v >  0)   return "#e8e070";
      return "#bcd4bc";
    }
    if (scaleName === "pct_normal_green") {
      // % of normal yield/production — GREEN at and above normal; TAN/brown only
      // when WELL below (Kevin's call). Near-normal (~98-104%) already reads green
      // so "fine" looks healthy, not washed-out cream.
      if (v < 75)  return "#9a6a23";   // way below — deep tan/brown
      if (v < 85)  return "#c79a4e";   // well below — tan
      if (v < 92)  return "#e3cf94";   // below — pale tan
      if (v < 98)  return "#cfe0a0";   // just under normal — tan→green
      if (v < 104) return "#9fcf78";   // ~normal — light green
      if (v < 112) return "#5fb24f";   // above
      if (v < 122) return "#33903a";   // well above
      return "#1f7a2e";                // big crop — deep green
    }
    if (scaleName === "pct_div_adaptive") {
      // Diverging at 100% (= same as the comparison year), but DOMAIN-RELATIVE so
      // the ramp fills the actual spread instead of saturating at a fixed 122%.
      // Brown below 100, green above; intensity scaled to the adaptive domain.
      // domain = [100−half, 100+half] (see activeScale), so 100 stays neutral.
      if (v >= 100) {
        const t = hi > 100 ? Math.max(0, Math.min(0.9999, (v - 100) / (hi - 100))) : 0;
        const g = ["#cfe8b8", "#9fcf78", "#6bbb55", "#3a9a3c", "#26802a", "#155a1d"];
        return g[Math.floor(t * g.length)];
      }
      const t = lo < 100 ? Math.max(0, Math.min(0.9999, (100 - v) / (100 - lo))) : 0;
      const b = ["#ecdcb0", "#dcc07a", "#c79a4e", "#a8762f", "#85591c"];
      return b[Math.floor(t * b.length)];
    }
    if (scaleName === "diverging_precip_pct") {
      // % of normal — fine 10-pt diverging bands (brown=dry → cream≈100% → green=wet).
      // Dry side runs below 60 too — Allegany/Cattaraugus hit the 30s-50s, and those
      // bands MUST exist or they fall under the lowest contour and render blank.
      if (v < 30) return "#4f2705";
      if (v < 40) return "#5f3207";
      if (v < 50) return "#6e3809";
      if (v < 60) return "#7a3e0a";
      if (v < 70) return "#a05c16";
      if (v < 80) return "#c07f2a";
      if (v < 90) return "#dba85a";
      if (v < 100) return "#f0d49a";
      if (v < 110) return "#eef1e6";   // ~normal: cream
      if (v < 120) return "#cfe8c5";   // wet → green ramp (was blue)
      if (v < 130) return "#a3d49a";
      if (v < 140) return "#6fbf73";
      if (v < 150) return "#3f9e4d";
      if (v < 160) return "#2d7d32";
      return "#1b5e20";
    }
    if (scaleName === "diverging_precip") {
      // brown (dry) → cream → green (wet)
      const tt = (v - lo) / (hi - lo);
      if (tt < 0.2) return "#8b5a14";
      if (tt < 0.4) return "#d4a060";
      if (tt < 0.55) return "#f0e0c0";
      if (tt < 0.7) return "#a3d49a";
      if (tt < 0.85) return "#4f9e4d";
      return "#1b5e20";
    }
    if (scaleName === "diverging_gdd" || scaleName === "diverging_gdd_pct") {
      // blue (cold) → cream → red (hot)
      const tt = (v - lo) / (hi - lo);
      if (tt < 0.2) return "#1a5a8a";
      if (tt < 0.4) return "#4a8eb8";
      if (tt < 0.55) return "#f0e0c0";
      if (tt < 0.7) return "#f0b89c";
      if (tt < 0.85) return "#e08060";
      return "#c04030";
    }
    return "#cccccc";
  }
  // D3 projection
  const { pathGen, centroids, projection } = React.useMemo(() => {
    if (!geoData || !d3Lib) return { pathGen: null, centroids: {}, projection: null };
    const proj = d3Lib.geoMercator().fitSize([800, 460], geoData);
    const pg = d3Lib.geoPath().projection(proj);
    const cents = {};
    geoData.features.forEach((f) => { const c = pg.centroid(f); if (c && !isNaN(c[0])) cents[f.properties.name] = c; });
    return { pathGen: pg, centroids: cents, projection: proj };
  }, [geoData, d3Lib]);
  // ── Exact 1 km rainfall overlay — OUR native MRMS raingrid, client-rendered ──
  // The radar layers now render our own gauge-corrected 1 km data (windows 1/2/3 days)
  // instead of IEM's WMS: exact grib values (not colortable-binned) and consistent with
  // the per-field rainfall. Painted with the MRMS colortable (mrmsLutColor) into an
  // EPSG:3857 image over the map bounds, so the existing hover/legend keep working.
  // Rolling latest {24|48|72}H accumulation file → fresh through this afternoon (not just
  // through last midnight). One grib fetch, cached ~30 min server-side.
  const radarProd = M && M.isRadar ? ({ mrms_p24h: 24, mrms_p48h: 48, mrms_p72h: 72 }[M.radar] || 24) : null;
  const [rainGrid, setRainGrid] = React.useState(null);   // { la0, lo0, step, ni, nj, inches[], ends }
  React.useEffect(() => {
    if (!radarProd) { setRainGrid(null); return; }
    let alive = true; setRainGrid(null);
    fetch(`/api/wny-weather-sync?mode=raingrid&prod=${radarProd}`)
      .then((r) => r.json()).then((d) => { if (alive && d && d.inches) setRainGrid(d); }).catch(() => {});
    return () => { alive = false; };
  }, [radarProd]);
  const radarOverlay = React.useMemo(() => {
    if (!M || !M.isRadar || !rainGrid || !projection || !geoData || !d3Lib || !d3Lib.geoBounds) return null;
    const [[w, s], [e, n]] = d3Lib.geoBounds(geoData);
    const tl = projection([w, n]), br = projection([e, s]);
    if (!tl || !br || isNaN(tl[0]) || isNaN(br[0])) return null;
    const R = 6378137, to3857 = (lon, lat) => [R * lon * Math.PI / 180, R * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2))];
    const [minx, miny] = to3857(w, s), [maxx, maxy] = to3857(e, n);
    const PXW = 700, PXH = Math.max(160, Math.round(PXW * (maxy - miny) / (maxx - minx)));
    const { la0, lo0, step, ni, nj, inches } = rainGrid;
    let url;
    try {
      const cv = document.createElement("canvas"); cv.width = PXW; cv.height = PXH;
      const ctx = cv.getContext("2d"), im = ctx.createImageData(PXW, PXH), data = im.data;
      for (let py = 0; py < PXH; py++) {
        const Y = maxy - (py + 0.5) / PXH * (maxy - miny);
        const lat = (2 * Math.atan(Math.exp(Y / R)) - Math.PI / 2) * 180 / Math.PI;
        const j = Math.round((lat - la0) / step);
        for (let px = 0; px < PXW; px++) {
          const lon = (minx + (px + 0.5) / PXW * (maxx - minx)) / R * 180 / Math.PI;
          const i = Math.round((lon - lo0) / step);
          const o = (py * PXW + px) * 4;
          const c = (i < 0 || i >= ni || j < 0 || j >= nj) ? null : mrmsLutColor(inches[j * ni + i]);
          if (!c) { data[o + 3] = 0; continue; }
          data[o] = c[0]; data[o + 1] = c[1]; data[o + 2] = c[2]; data[o + 3] = 235;
        }
      }
      ctx.putImageData(im, 0, 0);
      url = cv.toDataURL("image/png");
    } catch (_) { return null; }
    return { url, x: tl[0], y: tl[1], w: br[0] - tl[0], h: br[1] - tl[1], pxw: PXW, pxh: PXH };
  }, [M, rainGrid, projection, geoData, d3Lib]);
  // Off-screen canvas of the radar PNG so we can read the rain value under the cursor.
  // Color→inches via MRMS_RAMP_LUT (decoded from IEM's own MRMS Q3 24h legend; verified
  // the 48h/72h layers share the same colortable). Image is CORS-enabled, so no taint.
  const radarCanvasRef = React.useRef(null);
  const [radarTip, setRadarTip] = React.useState(null);   // { x, y, val } in SVG coords
  // Per-cell / per-county value readout under the cursor for the choropleth metrics
  // (yield, soil AWC, dry-down, rainfall, etc.) — the non-radar analog of radarTip.
  const [hoverTip, setHoverTip] = React.useState(null);   // { x, y, val } in SVG coords
  const cellRowsRef = React.useRef([]);                   // latest cellRows, read at hover time
  React.useEffect(() => {
    setRadarTip(null);
    if (!radarOverlay) { radarCanvasRef.current = null; return; }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const cv = document.createElement("canvas");
        cv.width = radarOverlay.pxw; cv.height = radarOverlay.pxh;
        cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
        radarCanvasRef.current = cv;
      } catch (_) { radarCanvasRef.current = null; }
    };
    img.onerror = () => { if (!cancelled) radarCanvasRef.current = null; };
    img.src = radarOverlay.url;
    return () => { cancelled = true; radarCanvasRef.current = null; };
  }, [radarOverlay ? radarOverlay.url : null]);
  // Sample the radar canvas at fractional (fx,fy) of the image → nearest ramp value (inches) or null.
  const sampleRadar = useCallback((fx, fy) => {
    const cv = radarCanvasRef.current; if (!cv) return null;
    const px = Math.min(cv.width - 1, Math.max(0, Math.floor(fx * cv.width)));
    const py = Math.min(cv.height - 1, Math.max(0, Math.floor(fy * cv.height)));
    let d; try { d = cv.getContext("2d").getImageData(px, py, 1, 1).data; } catch (_) { return null; }
    if (d[3] < 100) return null;                      // transparent → no QPE there
    let best = Infinity, bv = null;
    for (const [r, g, b, v] of MRMS_RAMP_LUT) { const dr = d[0] - r, dg = d[1] - g, db = d[2] - b; const dist = dr * dr + dg * dg + db * db; if (dist < best) { best = dist; bv = v; } }
    return best > 4000 ? null : bv;                   // far from any ramp color (gray/lake/border) → no data
  }, []);
  // SVG mousemove: keep the existing pan logic, and (radar metric only) read the
  // rain value under the cursor. Cursor→SVG coords use the same viewBox math as zoom,
  // so it stays correct through pan/zoom.
  const onSvgMouseMove = useCallback((e) => {
    onMapMove(e);
    if (dragRef.current || isMobile || !M) return;
    const r = e.currentTarget.getBoundingClientRect();
    const svgX = view.x + (e.clientX - r.left) / r.width * view.w;
    const svgY = view.y + (e.clientY - r.top) / r.height * view.h;
    // Town name on hover (independent of the active metric) — find the township
    // under the cursor via geoContains; label it at the town's centroid so only
    // one name shows at a time and the map never gets text-busy.
    if (showTowns && townFeatures && townFeatures.length && projection && projection.invert && d3Lib && d3Lib.geoContains && pathGen) {
      let tll; try { tll = projection.invert([svgX, svgY]); } catch (_) { tll = null; }
      const tf = (tll && !isNaN(tll[0])) ? townFeatures.find((f) => d3Lib.geoContains(f, tll)) : null;
      setHoveredTown(tf ? { name: tf.properties.NAME, x: svgX, y: svgY } : null);
    }
    // Radar layers: read the rain value from the live image (existing behavior).
    if (M.isRadar && radarOverlay) {
      const fx = (svgX - radarOverlay.x) / radarOverlay.w, fy = (svgY - radarOverlay.y) / radarOverlay.h;
      if (fx < 0 || fx > 1 || fy < 0 || fy > 1) { setRadarTip(null); return; }
      const val = sampleRadar(fx, fy);
      setRadarTip(val == null ? null : { x: svgX, y: svgY, val });
      return;
    }
    // Choropleth metrics: invert cursor → lon/lat, then read the value under it.
    // Per-cell (high-def) metrics sample the nearest gridMET cell; county-level
    // metrics use the hovered county. Either way a small value box follows the cursor.
    if (!projection || !projection.invert) { if (hoverTip) setHoverTip(null); return; }
    let ll; try { ll = projection.invert([svgX, svgY]); } catch (_) { ll = null; }
    if (!ll || isNaN(ll[0]) || isNaN(ll[1])) { if (hoverTip) setHoverTip(null); return; }
    const [lon, lat] = ll;
    let val = null, cell = null;
    if (M.cellAccessor) {
      const rows = cellRowsRef.current || [];
      let best = Infinity, bc = null;
      for (const a of rows) { const dx = a.lon - lon, dy = a.lat - lat; const d = dx * dx + dy * dy; if (d < best) { best = d; bc = a; } }
      if (bc && best < 0.06) { const v = M.cellAccessor(bc); if (v != null && !isNaN(v)) { val = Number(v); cell = bc; } }
    } else if (M.getter) {
      // Read the county polygon directly under the cursor (accurate + instant —
      // no dependence on the hovered-state render lag, which made county-metric
      // tooltips feel flaky). Falls back to the hovered county if geoContains
      // is unavailable.
      let cty = null;
      if (d3Lib && d3Lib.geoContains && geoData) {
        const f = geoData.features.find((ft) => d3Lib.geoContains(ft, [lon, lat]));
        if (f) cty = byName[String(f.properties.name).toUpperCase()];
      }
      if (!cty && hovered) cty = byName[String(hovered).toUpperCase()];
      if (cty) { const v = M.getter(cty); if (v != null && !isNaN(v)) val = Number(v); }
    }
    setHoverTip(val == null ? null : { x: svgX, y: svgY, val, cell });
  }, [onMapMove, isMobile, M, radarOverlay, view, sampleRadar, projection, hovered, byName, hoverTip, d3Lib, geoData, showTowns, townFeatures, pathGen]);
  const onSvgLeave = useCallback(() => { onMapUp(); setRadarTip(null); setHoverTip(null); setHoveredTown(null); }, [onMapUp]);
  // ── Smooth choropleth surface (d3.contours over a 0.05° IDW-interpolated grid) ──
  // Works for both per-cell metrics (cellAccessor on anomaly rows) and
  // county-level metrics (county centroids as IDW control points).
  //
  // THE LATTICE WAS A HARDCODED WNY BOX, AND THAT IS WHY THE EAST DREW EMPTY.
  // It was `SURF_LON_MIN -79.75, SURF_LAT_MAX 43.45, 72×33 @0.05°` — a window of
  // lon -79.75→-76.20, lat 41.85→43.45, whose own comment records how it got
  // there: "extended east to -76.15 so eastern Cayuga isn't clipped". Tuned to
  // the 16 counties, one county at a time. At 39 counties every cell east of
  // -76.20 or north of 43.45 fell outside the lattice, so the contour generator
  // had nothing to draw there and St Lawrence through Dutchess rendered as bare
  // outlines beside a painted west — 39 outlines with 16 filled, which reads as
  // broken rather than as a boundary. Third time this page has failed that way.
  //
  // Bounds are now DERIVED from the counties actually being drawn, so the lattice
  // follows the region toggle exactly as the projection does: Western NY gets a
  // ~68×31 window over the same ground it always had, All New York gets the state.
  const SURF = React.useMemo(() => {
    const snap = (v, dir) => (dir < 0 ? Math.floor(v * 20) / 20 : Math.ceil(v * 20) / 20);
    const fallback = { lonMin: -79.75, latMax: 43.45, step: 0.05, cols: 72, rows: 33 };
    if (!geoData || !d3Lib || !d3Lib.geoBounds || !geoData.features || !geoData.features.length) return fallback;
    let bounds; try { bounds = d3Lib.geoBounds(geoData); } catch (_) { return fallback; }
    const [[w, s], [e, n]] = bounds;
    if (![w, s, e, n].every(Number.isFinite)) return fallback;
    const PAD = 0.05;   // one lattice step of bleed so edge counties aren't clipped
    const lonMin = snap(w - PAD, -1), lonMax = snap(e + PAD, 1);
    const latMin = snap(s - PAD, -1), latMax = snap(n + PAD, 1);
    // IDW is O(gridCells × controlPoints); control points tripled with the eastern
    // build (338 → 1,271 cells), so the lattice is capped rather than left to grow
    // with the extent. Coarsening the step is the honest lever — a surface drawn
    // at 0.075° still reads smooth, a page that blocks for a second does not.
    let step = 0.05, cols, rows;
    for (let i = 0; i < 6; i++) {
      cols = Math.round((lonMax - lonMin) / step) + 1;
      rows = Math.round((latMax - latMin) / step) + 1;
      if (cols * rows <= 14000) break;
      step *= 1.5;
    }
    return { lonMin, latMax, step, cols, rows };
  }, [geoData, d3Lib]);

  // IDW over the 8 nearest control points, power 2.
  //
  // Bucketed, and not as a micro-optimisation: the old inner loop allocated one
  // object PER CONTROL POINT PER GRID CELL and then full-sorted them. At the WNY
  // 16 that was 2,376 cells × 338 points; statewide it would be ~10,600 × 1,271 =
  // 13.5M allocations plus 10,600 sorts of a 1,271-element array, twice over
  // (the corn-mute layer builds its own grid). Bucketing to a 0.25° hash and
  // scanning outward keeps the work proportional to the NEIGHBOURS of a cell
  // rather than to the whole state, and the 8-nearest selection is an insertion
  // into a fixed 8-slot array — no allocation, no sort.
  //
  // Measured against the old implementation on the real 1,271-point set:
  //   Western NY   68×31  =  2,108 cells   438 ms -> 8 ms
  //   All New York 133×80 = 10,640 cells 1,959 ms -> 25 ms
  // The old one at statewide size would block ~2 s per grid and this page builds
  // TWO (surface + corn mute) on every metric change.
  //
  // NOT BIT-IDENTICAL, and the difference is worth naming rather than hiding:
  // the 8 points chosen are at IDENTICAL DISTANCES (max distance delta 0.00
  // across 2,680 probe positions), so the selection is the true 8-nearest. But
  // control points sit on a regular 0.1° lattice, so a query point is often
  // exactly equidistant from several of them — 254 of those 2,680 positions had
  // a tie at the 8th slot, and the two implementations break it in different
  // arbitrary orders (old: JS sort order; new: bucket scan order). On a smooth
  // field that moves the interpolated value by at most 0.47 bu, at tie positions
  // only, against contour bands ~12 bu wide. Neither tie-break was ever
  // "correct". County numbers are untouched — this is the painted surface only.
  function buildSurfaceGrid(points) {
    if (!points.length) return null;
    const { lonMin, latMax, step, cols, rows } = SURF;
    const B = 0.25;                                   // bucket size in degrees
    const bkey = (bx, by) => bx * 100000 + by;
    const buckets = new Map();
    for (const p of points) {
      const k = bkey(Math.floor(p.lon / B), Math.floor(p.lat / B));
      let arr = buckets.get(k); if (!arr) buckets.set(k, (arr = []));
      arr.push(p);
    }
    const out = new Array(rows * cols);
    const dBest = new Float64Array(8), vBest = new Float64Array(8);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const lon = lonMin + c * step, lat = latMax - r * step;
        let nBest = 0, worst = Infinity;
        const consider = (p) => {
          const dx = p.lon - lon, dy = p.lat - lat, d = dx * dx + dy * dy;
          if (nBest === 8 && d >= worst) return;
          let i = nBest < 8 ? nBest++ : 7;
          while (i > 0 && dBest[i - 1] > d) { dBest[i] = dBest[i - 1]; vBest[i] = vBest[i - 1]; i--; }
          dBest[i] = d; vBest[i] = p.v;
          worst = dBest[nBest - 1];
        };
        // expand the bucket ring until 8 candidates are in hand, then ONE more
        // ring — the nearest 8 by bucket are not necessarily the nearest 8 by
        // distance until the ring covers the current worst radius.
        const bx0 = Math.floor(lon / B), by0 = Math.floor(lat / B);
        for (let ring = 0; ring <= 12; ring++) {
          for (let bx = bx0 - ring; bx <= bx0 + ring; bx++) {
            for (let by = by0 - ring; by <= by0 + ring; by++) {
              if (ring > 0 && Math.abs(bx - bx0) !== ring && Math.abs(by - by0) !== ring) continue; // ring perimeter only
              const arr = buckets.get(bkey(bx, by));
              if (arr) for (let i = 0; i < arr.length; i++) consider(arr[i]);
            }
          }
          if (nBest === 8 && (ring * B) * (ring * B) >= worst) break;
        }
        let wsum = 0, vsum = 0;
        for (let i = 0; i < nBest; i++) {
          if (dBest[i] === 0) { wsum = 1; vsum = vBest[i]; break; }
          const w = 1 / dBest[i];                // 1/d² since d is already squared
          vsum += vBest[i] * w; wsum += w;
        }
        out[r * cols + c] = wsum > 0 ? vsum / wsum : 0;
      }
    }
    return out;
  }
  // Per-cell rows for the high-def surface: anomaly rows joined with CDL
  // corn_acres (by lat/lon) + per-cell predicted PRODUCTION. Cell production =
  // county predicted production (already net of silage/PP) distributed across
  // the county's corn cells by corn-acre share — so it sums back to the county
  // number and is structurally ZERO where there's no corn (cities, forest,
  // water). This is what lets the map show bushels where the corn actually is.
  const cellRows = React.useMemo(() => {
    if (!anomalies.length) return [];
    const NCFB = 0.4;   // NCCPI fallback for a rare null cell
    const cornByLL = {}, fipsByLL = {}, nccpiByLL = {}, awcByLL = {};
    for (const c of wnyGridCells) {
      if (c.lat == null || c.lon == null) continue;
      const k = `${c.lat.toFixed(2)}|${c.lon.toFixed(2)}`;
      cornByLL[k] = c.corn_acres; fipsByLL[k] = c.county_fips; nccpiByLL[k] = c.nccpi_corn; awcByLL[k] = c.awc;
    }
    const prodByFips = {}, yieldByFips = {}, waterByFips = {};
    for (const c of counties || []) { if (!c.error) { if (c.pred_prod != null) prodByFips[c.fips] = c.pred_prod; if (c.forecast_yield != null) yieldByFips[c.fips] = c.forecast_yield; if (c.water_balance && c.water_balance.factor != null) waterByFips[c.fips] = c.water_balance.factor; } }
    // Per-cell soil-water bucket factor (lat|lon → factor), computed by the live
    // model. Lets the map show THIS-YEAR sub-county dry-down (gravelly ground draws
    // its bank down faster than valley silt-loam) and re-bases per-cell yield off it.
    const cellWaterByLL = (gridShadow && gridShadow.cell_water) || {};
    const cellDiseaseByLL = (gridShadow && gridShadow.cell_disease) || {};
    // per-county production weight (Σ corn×soil) + corn-weighted mean soil
    const totPWByFips = {}, cornByFips = {};
    for (const c of wnyGridCells) {
      if (c.corn_acres > 0 && c.county_fips) {
        const nc = c.nccpi_corn != null ? Number(c.nccpi_corn) : NCFB;
        totPWByFips[c.county_fips] = (totPWByFips[c.county_fips] || 0) + Number(c.corn_acres) * nc;
        cornByFips[c.county_fips] = (cornByFips[c.county_fips] || 0) + Number(c.corn_acres);
      }
    }
    return anomalies.map((a) => {
      const k = `${a.lat.toFixed(2)}|${a.lon.toFixed(2)}`;
      const corn = cornByLL[k] != null ? cornByLL[k] : null;
      const fips = fipsByLL[k];
      const nccpi = nccpiByLL[k] != null ? Number(nccpiByLL[k]) : null;
      const nc = nccpi != null ? nccpi : NCFB;
      let cellProd = null, cellYield = null;
      const cellW = cellWaterByLL[k] != null ? cellWaterByLL[k] : null;   // this cell's soil-water factor
      if (fips) {
        // production (bushels): county pred_prod distributed by corn×soil — where
        // the bushels come from. Needs loaded corn; null elsewhere (corn-specific).
        if (corn != null && prodByFips[fips] != null && totPWByFips[fips] > 0) cellProd = prodByFips[fips] * (corn * nc / totPWByFips[fips]);
        // YIELD — COMPLETE coverage (no holes): sub-county soil detail where corn
        // is loaded, flat county forecast where it isn't. Soil spread DAMPENED ±15%
        // (long-run potential — NCCPI bakes in drought vulnerability a wet year can
        // neutralize/flip; the this-year soil×weather version is the next build).
        if (yieldByFips[fips] != null) {
          if (corn != null && corn > 0 && cornByFips[fips] > 0 && totPWByFips[fips] > 0) {
            const meanNc = totPWByFips[fips] / cornByFips[fips];
            cellYield = meanNc > 0 ? yieldByFips[fips] * Math.max(0.85, Math.min(1.15, nc / meanNc)) : yieldByFips[fips];
          } else {
            cellYield = yieldByFips[fips];   // flat county yield where no/zero corn data
          }
          // THIS-YEAR soil×weather: re-base off the cell's own soil-water bucket
          // factor. County yield already bakes in the county-mean water factor, so
          // scaling by (cellWater / countyWater) expresses this cell's dry-down
          // (droughty/low-AWC ground shows lower yield). Missing → no change.
          if (cellYield != null && cellW != null && waterByFips[fips] > 0) {
            cellYield = cellYield * (cellW / waterByFips[fips]);
          }
        }
      }
      const dz = cellDiseaseByLL[k] || null;
      return { ...a, fips, corn_acres: corn, nccpi_corn: nccpi, awc: awcByLL[k] != null ? Number(awcByLL[k]) : null, cell_prod: cellProd, cell_yield: cellYield, water_factor: cellW, water_drag_pct: cellW != null ? Math.round((1 - cellW) * 1000) / 10 : null, precip_pct_window: windowPct[k] != null ? windowPct[k] : null, precip_in_window: windowIn[k] != null ? windowIn[k] : null, forecast_rain: forecastRain[k] != null ? forecastRain[k] : null, disease: dz, disease_score: dz ? dz.score : null };
    });
  }, [anomalies, wnyGridCells, counties, windowPct, windowIn, gridShadow, forecastRain]);
  const layerDrawsCells = !!(M && typeof M.cellAccessor === "function");
  const cellSurfacePending = layerDrawsCells && gridLoad === "loading" && !cellRows.length;
  // Keep the hover handler's cell snapshot fresh (read via ref so the handler
  // identity doesn't churn on every recompute), and clear any stale tooltip
  // when the active metric changes.
  React.useEffect(() => { cellRowsRef.current = cellRows; }, [cellRows]);
  // ── COVERAGE IS MEASURED, NOT DECLARED ──────────────────────────────────────
  // Which counties a cell layer can speak for is read off the CELLS THEMSELVES —
  // a county counts only where at least one of its cells carries a non-null value
  // for THIS metric. Keying it on "does the county have cells" would have been a
  // declaration, and it breaks the moment a county is half-built: cells inserted
  // with CDL corn but no gridMET yet would claim the rainfall layers and the IDW
  // surface would quietly bleed a neighbour's rain across them. Measuring costs
  // one pass over ~1,300 rows per metric change and cannot drift.
  const layerFipsWithData = React.useMemo(() => {
    const out = new Set();
    if (!M || typeof M.cellAccessor !== "function") return out;
    for (const a of cellRows) {
      if (!a.fips) continue;
      if (out.has(String(a.fips))) continue;
      const v = M.cellAccessor(a);
      if (v != null && !isNaN(v)) out.add(String(a.fips));
    }
    return out;
  }, [M, cellRows]);
  // ── WHICH COUNTIES CAN THE ACTIVE LAYER SPEAK FOR? ──────────────────────────
  //   · radar   — MRMS is an image over the state; no county, no cell, always on
  //   · pending — the two gridShadow layers load lazily; hatching the whole map
  //               while they arrive would read as "this layer does not exist"
  //   · cell    — measured above, per metric
  //   · model   — needs a county row out of the yield model (>= 6 NASS yield yrs)
  // Anything it cannot speak for is hatched by the fill, never dropped.
  const layerHasData = React.useCallback((fips) => {
    if (M && M.isRadar) return true;
    if (cellLayersPending) return true;
    return layerIsCellData ? layerFipsWithData.has(String(fips)) : !!byFips[String(fips)];
  }, [M, cellLayersPending, layerIsCellData, layerFipsWithData, byFips]);
  // How much of what is DRAWN the active layer actually covers. Printed whenever
  // anything is hatched, because a grey county has to carry a number and a
  // reason — grey is not a low value. (Same rule as the PRF grid map.)
  const layerCoverage = React.useMemo(() => {
    const feats = (geoData && geoData.features) || [];
    let has = 0;
    for (const f of feats) if (layerHasData(f.properties.fips)) has++;
    return { has, total: feats.length, missing: feats.length - has };
  }, [geoData, layerHasData]);
  // Only the thin counties actually DRAWN in the current view — Western NY
  // holds none of them, so naming all four there would be a caveat about ground
  // the reader is not looking at.
  const thinCountiesShown = React.useMemo(() => {
    const drawn = new Set(((geoData && geoData.features) || []).map((f) => f.properties.fips));
    return MI_MODEL_COUNTIES.filter((c) => miCountyIsThin(c) && drawn.has(c.fips));
  }, [geoData]);
  React.useEffect(() => { setHoverTip(null); }, [metric]);
  const surfaceContours = React.useMemo(() => {
    // flatCounty metrics are county-REPORTED data (yield/production/inventory/
    // drought), not a continuous physical field — render as discrete per-county
    // fills (getFill), never an interpolated surface that bleeds across lines.
    if (!d3Lib || !d3Lib.contours || !projection || !M || !M.thresholds || M.flatCounty) return null;
    const pts = [];
    if (M.cellAccessor) {
      for (const a of cellRows) {
        const v = M.cellAccessor(a);
        if (v != null && !isNaN(v)) pts.push({ lon: a.lon, lat: a.lat, v: Number(v) });
      }
    } else if (M.getter) {
      for (const c of counties || []) {
        if (c.error) continue;
        const ll = countyCentroidsLL[String(c.fips)];
        if (!ll) continue;
        const v = M.getter(c);
        if (v != null && !isNaN(v)) pts.push({ lon: ll[0], lat: ll[1], v: Number(v) });
      }
    }
    if (pts.length < 2) return null;
    const values = buildSurfaceGrid(pts);
    if (!values) return null;
    const contours = d3Lib.contours().size([SURF.cols, SURF.rows]).thresholds(M.thresholds)(values);
    const polyPath = (mp) => {
      let d = "";
      for (const polygon of mp.coordinates) {
        for (const ring of polygon) {
          for (let i = 0; i < ring.length; i++) {
            const gx = ring[i][0], gy = ring[i][1];
            const lon = SURF.lonMin + gx * SURF.step;
            const lat = SURF.latMax - gy * SURF.step;
            const sp = projection([lon, lat]);
            if (!sp) continue;
            d += (i === 0 ? "M" : "L") + sp[0].toFixed(1) + "," + sp[1].toFixed(1);
          }
          d += "Z";
        }
      }
      return d;
    };
    return contours.map((c) => ({ value: c.value, path: polyPath(c) }));
  }, [d3Lib, projection, M, cellRows, counties, countyCentroidsLL, SURF]);
  // Corn-density MUTE layer: for the corn-dependent metrics, fade the surface
  // toward the base color wherever there's little/no corn (cities, forest, water)
  // so the map only "lights up" where corn actually grows — keeping the smooth
  // interpolated look but honest about where it's relevant. Alpha is inverse to
  // interpolated corn density; high-corn cells stay vivid (alpha→0).
  const cornMaskCells = React.useMemo(() => {
    if (!d3Lib || !projection || !M || !M.muteCorn || !cellRows.length) return null;
    const pts = [];
    for (const a of cellRows) { if (a.corn_acres != null && !isNaN(a.corn_acres)) pts.push({ lon: a.lon, lat: a.lat, v: Number(a.corn_acres) }); }
    if (pts.length < 2) return null;
    const grid = buildSurfaceGrid(pts);
    if (!grid) return null;
    const MUTE_FULL = 1200;   // corn ac/cell at/above which no mute (full color)
    const cells = [];
    for (let r = 0; r < SURF.rows; r++) for (let c = 0; c < SURF.cols; c++) {
      const corn = grid[r * SURF.cols + c] || 0;
      const alpha = Math.max(0, Math.min(0.82, (1 - corn / MUTE_FULL) * 0.82));
      if (alpha < 0.05) continue;   // high-corn → leave vivid
      const lon0 = SURF.lonMin + (c - 0.5) * SURF.step, lon1 = SURF.lonMin + (c + 0.5) * SURF.step;
      const lat0 = SURF.latMax - (r - 0.5) * SURF.step, lat1 = SURF.latMax - (r + 0.5) * SURF.step;
      const q = [[lon0, lat0], [lon1, lat0], [lon1, lat1], [lon0, lat1]].map(([x, y]) => projection([x, y]));
      if (q.some((p) => !p)) continue;
      cells.push({ pts: q.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" "), alpha: Math.round(alpha * 100) / 100 });
    }
    return cells;
  }, [d3Lib, projection, M, cellRows, SURF]);
  // Project PRF grid squares to SVG coordinates. Each grid is 0.25° square,
  // colored by rainfall amount for the selected interval. Normal-relative
  // color (% of normal) is more useful than raw inches for a stress read.
  const prfGridShapes = React.useMemo(() => {
    if (!projection || !showPrfGrids) return [];
    // Build a lat/lon-keyed lookup so we can find neighbors for outlier detection
    const gridByLatLon = {};
    const gridKey = (lat, lon) => `${lat.toFixed(3)}|${lon.toFixed(3)}`;
    for (const g of prfGrids) gridByLatLon[gridKey(g.lat, g.lon)] = g;
    const out = [];
    for (const g of prfGrids) {
      const data = prfRainByGrid[g.grid_id]?.[prfInterval];
      if (!data || data.rain == null) continue;
      // Outlier detection: if this grid's rainfall is < 30% of the median of
      // its 4 cardinal neighbors AND that median > 3", it's almost certainly
      // a bad data point (NOAA-CPC sync gap for this specific grid).
      // 0.25° step in lat/lon, so neighbors are at ±0.25.
      const neighborRains = [];
      for (const [dlat, dlon] of [[0.25, 0], [-0.25, 0], [0, 0.25], [0, -0.25]]) {
        const n = gridByLatLon[gridKey(g.lat + dlat, g.lon + dlon)];
        if (n) {
          const nr = prfRainByGrid[n.grid_id]?.[prfInterval]?.rain;
          if (nr != null && nr > 0) neighborRains.push(nr);
        }
      }
      neighborRains.sort((a, b) => a - b);
      const neighborMedian = neighborRains.length ? neighborRains[Math.floor(neighborRains.length / 2)] : null;
      const isOutlier = neighborMedian != null && neighborMedian > 3 && data.rain < neighborMedian * 0.3;
      // 0.25° square corners
      const corners = [
        [g.lon - 0.125, g.lat + 0.125],
        [g.lon + 0.125, g.lat + 0.125],
        [g.lon + 0.125, g.lat - 0.125],
        [g.lon - 0.125, g.lat - 0.125],
      ].map(([lng, lat]) => projection([lng, lat])).filter((p) => p && !isNaN(p[0]));
      if (corners.length !== 4) continue;
      const pts = corners.map((p) => `${p[0]},${p[1]}`).join(" ");
      out.push({
        grid_id: g.grid_id, lat: g.lat, lon: g.lon,
        rain: data.rain,
        normal: data.normal,
        pct: data.normal && data.normal > 0 ? data.rain / data.normal : null,
        is_outlier: isOutlier,
        neighbor_median: neighborMedian,
        pts,
        cx: (corners[0][0] + corners[2][0]) / 2,
        cy: (corners[0][1] + corners[2][1]) / 2,
      });
    }
    return out;
  }, [projection, prfGrids, prfRainByGrid, prfInterval, showPrfGrids]);
  // WNY weather-grid dots — project each 0.1° cell; color by county so cell
  // placement (and county assignment) is easy to eyeball.
  const WNY_DOT_COLORS = ["#2d6a2d","#1a5a8a","#b8860b","#8b1c0c","#5a2d82","#0f7a6a","#a0522d","#c04030","#3a7d3a","#1a7a9a","#9a7b0f","#7a1c4c","#2a4d82","#6a8b1a","#b85000","#4a4a8a"];
  const wnyCountyColor = (name) => {
    const i = MI_MODEL_COUNTIES.findIndex((c) => c.name === String(name).toUpperCase());
    return WNY_DOT_COLORS[(i >= 0 ? i : 0) % WNY_DOT_COLORS.length];
  };
  const wnyGridDots = React.useMemo(() => {
    if (!projection || !showWnyGrid) return [];
    // Color each dot BY the active per-cell metric (saturated, high-contrast points —
    // far more legible than the faint IDW surface underneath). Fall back to county
    // color only for county-level metrics (no cellAccessor), keeping dots a
    // placement guide there. Join dot → cellRows value by the lat|lon key.
    const perCell = M && typeof M.cellAccessor === "function";
    const byLL = {};
    if (perCell) for (const cr of cellRows) byLL[`${Number(cr.lat).toFixed(2)}|${Number(cr.lon).toFixed(2)}`] = M.cellAccessor(cr);
    const out = [];
    for (const c of wnyGridCells) {
      const p = projection([c.lon, c.lat]);
      if (!p || isNaN(p[0])) continue;
      let fill = wnyCountyColor(c.county), val = null;
      if (perCell) {
        val = byLL[`${Number(c.lat).toFixed(2)}|${Number(c.lon).toFixed(2)}`];
        fill = (val == null || isNaN(val)) ? "#d8d8d4" : scaleColor(M.scale, val, M.domain);
      }
      out.push({ x: p[0], y: p[1], county: c.county, lat: c.lat, lon: c.lon, fill, val, fmt: perCell ? M.fmt : null });
    }
    return out;
  }, [projection, wnyGridCells, showWnyGrid, M, cellRows]);
  // Customer farm pins (in-area corn farms only). Render on top of grid dots
  // so the customer-facing data is visually dominant.
  const farmDots = React.useMemo(() => {
    // ⚠ THE THIRD GUARD, AND THE LAST ONE BEFORE A PIN IS DRAWN. The portal does not fetch
    // `farms` and does not render the control, so this can only fire if BOTH of those are
    // undone — which is exactly when a guard earns its place. A farm pin carries a name and
    // a coordinate; there is no version of a portal user seeing another grower's that is
    // acceptable, so the refusal sits at the point of drawing as well.
    if (isPortal) return [];
    if (!projection || !showFarms) return [];
    const out = [];
    for (const f of farms) {
      const p = projection([f.lon, f.lat]);
      if (!p || isNaN(p[0])) continue;
      out.push({ x: p[0], y: p[1], name: f.name, counties: f.counties });
    }
    return out;
  }, [projection, farms, showFarms, isPortal]);
  // Diverging color: dry (brown) → normal (yellow-cream) → wet (blue)
  function prfRainColor(pct, rawInches) {
    if (pct == null) {
      // Fall back to absolute scale if no normal (early-season intervals don't have normals)
      if (rawInches == null) return "rgba(200,200,200,0.4)";
      if (rawInches >= 6) return "rgba(26,90,138,0.85)";
      if (rawInches >= 4) return "rgba(74,142,184,0.8)";
      if (rawInches >= 2.5) return "rgba(170,210,230,0.75)";
      if (rawInches >= 1) return "rgba(240,224,192,0.75)";
      return "rgba(180,120,40,0.75)";
    }
    if (pct >= 1.5) return "rgba(26,90,138,0.85)";
    if (pct >= 1.2) return "rgba(74,142,184,0.8)";
    if (pct >= 0.9) return "rgba(170,210,230,0.75)";
    if (pct >= 0.7) return "rgba(240,224,192,0.75)";
    if (pct >= 0.5) return "rgba(212,160,96,0.8)";
    return "rgba(139,90,20,0.85)";
  }
  // Keyed by FIPS, not by name — see byFips. A name key missed St. Lawrence.
  const getFill = (fips) => {
    if (!M || typeof M.getter !== "function") return "#f2f2f0"; // cellAccessor metric — surface handles it
    const c = byFips[String(fips)];
    if (!c) return "#f2f2f0";
    const v = M.getter(c);
    return scaleColor(M.scale, v, activeScale.domain);
  };
  // Legend buckets for the active scale
  const legendBuckets = React.useMemo(() => {
    // Adaptive metrics: build the legend from the stretched domain so the strip
    // matches the colors actually painted (and the % bands move with the data).
    if (M && M.adaptive && activeScale && activeScale.domain) {
      const [lo, hi] = activeScale.domain; const n = 6; const out = [];
      for (let i = 0; i < n; i++) {
        const v1 = lo + (hi - lo) * (i + 1) / n, mid = lo + (hi - lo) * (i + 0.5) / n;
        out.push({ l: M.fmt ? M.fmt(v1) : v1.toFixed(0), c: scaleColor(M.scale, mid, activeScale.domain) });
      }
      return out;
    }
    if (M && M.isRadar) {
      // IEM MRMS Q3 scale (the actual colors + inch breakpoints decoded from IEM's
      // own legend), so the strip matches the radar image and the hover readout.
      return [
        { l: "0.25\"", c: "#0060ff" },
        { l: "0.5", c: "#00ccff" },
        { l: "0.75", c: "#00fe96" },
        { l: "1", c: "#00fe06" },
        { l: "2", c: "#9cfe00" },
        { l: "3", c: "#fe9c00" },
        { l: "4", c: "#fe0c00" },
        { l: "5", c: "#fe006c" },
        { l: "7", c: "#fe24fe" },
        { l: "10", c: "#fe84fe" },
        { l: "15\"+", c: "#fee4fe" },
      ];
    }
    const [lo, hi] = M.domain;
    if (M.scale === "drought") {
      return [
        { l: "None (0)", c: "#bcd4bc" },
        { l: "D0 (1-49)", c: "#e8e070" },
        { l: "D0+ (50-99)", c: "#e08060" },
        { l: "D1+ (100-199)", c: "#c04030" },
        { l: "D2+ (200+)", c: "#8b1c0c" },
      ];
    }
    if (M.scale === "diverging_precip_pct") {
      // % of normal — 10-pt bands match scaleColor exactly so the color reads as a %
      return [
        { l: "<40", c: "#5f3207" },
        { l: "40–50", c: "#6e3809" },
        { l: "50–60", c: "#7a3e0a" },
        { l: "60–70", c: "#a05c16" },
        { l: "70–80", c: "#c07f2a" },
        { l: "80–90", c: "#dba85a" },
        { l: "90–100", c: "#f0d49a" },
        { l: "100–110", c: "#eef1e6" },
        { l: "110–120", c: "#cfe8c5" },
        { l: "120–130", c: "#a3d49a" },
        { l: "130–140", c: "#6fbf73" },
        { l: "140–150", c: "#3f9e4d" },
        { l: "150–160", c: "#2d7d32" },
        { l: "≥160%", c: "#1b5e20" },
      ];
    }
    if (M.scale.startsWith("diverging")) {
      const isPrecip = M.scale === "diverging_precip";
      const labels = isPrecip
        ? ["Very dry", "Dry", "Near normal", "Wet", "Very wet"]
        : ["Cold", "Cool", "Near normal", "Warm", "Hot"];
      const palette = isPrecip
        ? ["#8b5a14", "#d4a060", "#f0e0c0", "#4f9e4d", "#1b5e20"]
        : ["#1a5a8a", "#4a8eb8", "#f0e0c0", "#e08060", "#c04030"];
      return labels.map((l, i) => ({ l, c: palette[i] }));
    }
    if (M.scale === "drydown") {
      // EXACTLY mirrors scaleColor("drydown") bands so the legend reads true
      // (the green→red dry-down ramp, not the green_high fallback).
      return [
        { l: "None / wetter", c: "#2e7d32" },
        { l: "−0 to 1.5%", c: "#7cb342" },
        { l: "−1.5 to 3.5%", c: "#cddc39" },
        { l: "−3.5 to 6%", c: "#f1c40f" },
        { l: "−6 to 10%", c: "#e8731c" },
        { l: "−10 to 15%", c: "#c0392b" },
        { l: "−15%+", c: "#7a1500" },
      ];
    }
    if (M.scale === "disease") {
      // mirrors scaleColor("disease") bands exactly
      return [
        { l: "<25% of days", c: "#f2efe4" },
        { l: "25–35", c: "#ddd0e0" },
        { l: "35–45", c: "#c3a8cf" },
        { l: "45–55", c: "#a077b6" },
        { l: "55–65", c: "#7a4b95" },
        { l: "≥65% — scout", c: "#54246e" },
      ];
    }
    if (M.scale === "pct_normal_green") {
      // mirrors scaleColor("pct_normal_green") — % of normal; tan below, green at/above
      return [
        { l: "<75%", c: "#9a6a23" },
        { l: "75–85", c: "#c79a4e" },
        { l: "85–92", c: "#e3cf94" },
        { l: "92–98", c: "#cfe0a0" },
        { l: "98–104", c: "#9fcf78" },
        { l: "104–112", c: "#5fb24f" },
        { l: "112–122", c: "#33903a" },
        { l: "≥122%", c: "#1f7a2e" },
      ];
    }
    if (M.scale === "rain_green") {
      // mirrors scaleColor("rain_green") — rainfall inches, pale (dry) → deep green (wet)
      return [
        { l: "<0.1\"", c: "#eef3e9" },
        { l: "0.1–0.25", c: "#d8ead0" },
        { l: "0.25–0.5", c: "#b5d99f" },
        { l: "0.5–1", c: "#86c46b" },
        { l: "1–2", c: "#4ea64a" },
        { l: "2–3.5", c: "#2d8a3e" },
        { l: "≥3.5\"", c: "#16632a" },
      ];
    }
    // Sequential
    const palettes = {
      green_high: ["#edf6ed", "#d6ebd4", "#bce0b6", "#9ed197", "#7cc077", "#58aa52", "#3a9038", "#277528", "#15531c"],
      red_high:   ["#fdeae0", "#f8cdb0", "#f0a578", "#e57a4a", "#cf4f2c", "#a83018", "#7a1c08"],
      blue_high:  ["#e0eaf2", "#b8d0e6", "#7ab0d6", "#4a8eb8", "#1a5a8a"],
      soil_div:   ["#a9772f", "#cdab6d", "#e6d9a8", "#a9c97e", "#5a9e4f", "#2d6a2d"],
      rain_adaptive: ["#eef6e9", "#d4ebc6", "#b1dc9c", "#86c46b", "#5bab4c", "#358f3d", "#1f6e2f", "#0c4d1e"],
    };
    const p = palettes[M.scale] || palettes.green_high;
    return p.map((c, i) => {
      const v = lo + ((hi - lo) * (i / (p.length - 1)));
      return { l: M.fmt(v), c };
    });
  }, [M, activeScale]);
  const hoveredCty = hovered ? byName[hovered.toUpperCase()] : null;
  const selectedCty = selected ? byFips[String(selected)] : null;
  // A county on the map that the YIELD MODEL has no row for. It still carries
  // real cell data (CDL corn acres, soil, weather), so clicking it answers the
  // question it raises — "why is this one blank?" — instead of doing nothing.
  const selectedGap = React.useMemo(() => {
    if (!selected || selectedCty) return null;
    const fips = String(selected);
    const name = FIPS_TO_NAME_GLOBAL[fips] || fips;
    let cells = 0, corn = 0;
    for (const c of wnyGridCells) {
      if (String(c.county_fips) !== fips) continue;
      cells++; if (c.corn_acres != null) corn += Number(c.corn_acres);
    }
    return { fips, name, cells, corn };
  }, [selected, selectedCty, wnyGridCells]);
  return (
    <div style={{ background: "#fff", border: "1px solid #dde7dd", borderRadius: 10, padding: 0, marginBottom: 16, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef3ee", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <span style={{ fontWeight: 800, color: "#1a2e1a" }}>🗺 NY County Map — color by metric</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Region toggle, ON THE MAP because that is where you notice you want it.
              It is the SAME `region` state the county table uses, passed down — not a
              second one. Two independent toggles for one filter is how a page ends up
              showing a 16-county map beside a 39-county headline. */}
          {setRegion && regions && MI_MODEL_COUNTIES.some((c) => !c.wny) && (
            <>
              <span style={{ display: "inline-flex", border: "1px solid #cfe0cf", borderRadius: 7, overflow: "hidden" }}>
                {Object.entries(regions).map(([key, r]) => (
                  <button key={key} onClick={() => setRegion(key)} title={r.hint}
                    style={{ fontSize: 11, fontWeight: region === key ? 800 : 600, padding: "4px 10px", cursor: "pointer", whiteSpace: "nowrap",
                             border: "none", borderLeft: key === "wny" ? "1px solid #cfe0cf" : "none",
                             background: region === key ? MI_GREEN : "#fff", color: region === key ? "#fff" : "#5a6f5a" }}>
                    {r.label}
                  </button>
                ))}
              </span>
              <span style={{ width: 1, height: 16, background: "#e0e8e0" }} />
            </>
          )}
          {setCatchment && (
            <>
              <button onClick={() => setDrawMode((v) => !v)} title="Define the elevator's draw area — click counties on the map to add or remove them"
                style={{ fontSize: 11, fontWeight: 800, padding: "4px 10px", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap", border: "1px solid " + (drawMode ? "#1559a3" : "#aac4e0"), background: drawMode ? "#1559a3" : "#eef3fb", color: drawMode ? "#fff" : "#1559a3" }}>
                {drawMode ? "✓ Done" : `🏭 Draw area${cat && cat.size ? ` (${cat.size})` : ""}`}
              </button>
              {drawMode && <span style={{ fontSize: 10.5, color: "#1559a3", fontWeight: 600 }}>click counties · {(cat && cat.size) || 0} selected</span>}
              {cat && cat.size > 0 && <button onClick={() => setCatchment(new Set())} style={{ fontSize: 11, fontWeight: 700, padding: "4px 8px", borderRadius: 6, cursor: "pointer", border: "1px solid #d9c2b2", background: "#fff", color: "#9a5a3a" }}>Clear</button>}
              <span style={{ width: 1, height: 16, background: "#e0e8e0" }} />
            </>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#3a5a3a", cursor: "pointer" }}>
            <input type="checkbox" checked={showPrfGrids} onChange={(e) => setShowPrfGrids(e.target.checked)} style={{ cursor: "pointer" }} />
            🌧 PRF rain grids
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#3a5a3a", cursor: "pointer" }}>
            <input type="checkbox" checked={showWnyGrid} onChange={(e) => setShowWnyGrid(e.target.checked)} style={{ cursor: "pointer" }} />
            📍 Weather grid ({wnyGridCells.length})
          </label>
          {/* ⚠ HIDE THE SURFACE, THEN GUARD THE FETCH — both halves, because either alone is
              one mistake from a leak. The fetch above returns nothing in the portal; this
              control does not exist there either, so a future edit that restores the read
              still puts no customer-farm layer in front of a grower or a prospect. */}
          {!isPortal && (
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#3a5a3a", cursor: "pointer" }}>
            <input type="checkbox" checked={showFarms} onChange={(e) => setShowFarms(e.target.checked)} style={{ cursor: "pointer" }} />
            🚜 Customer farms ({farms.length})
          </label>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#3a5a3a", cursor: "pointer" }} title="Civil township boundaries (Census TIGER). Outlines only — hover a town to see its name.">
            <input type="checkbox" checked={showTowns} onChange={(e) => setShowTowns(e.target.checked)} style={{ cursor: "pointer" }} />
            🗺 Town lines
          </label>
          {showPrfGrids && (
            <select value={prfInterval} onChange={(e) => setPrfInterval(e.target.value)} style={{ padding: "4px 8px", borderRadius: 5, border: "1px solid #ccd", fontSize: 11 }}>
              {["MARAPR", "APRMAY", "MAYJUN", "JUNJUL", "JULAUG", "AUGSEP", "SEPOCT"].map((iv) => <option key={iv} value={iv}>{iv} interval</option>)}
            </select>
          )}
        <select value={metric} onChange={(e) => setMetric(e.target.value)} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #ccd", fontSize: 12 }}>
          <optgroup label="── High-def grid (gridMET, 1,271 cells) ──">
            <option value="precip_pct_window">Rainfall — % of normal (pick dates)</option>
            <option value="precip_in_window">Rainfall (in) — pick dates</option>
            <option value="gdd_pct_normal">GDD — % of normal</option>
            <option value="forecast_rain">🌧 Forecast rain — pick days ahead (in)</option>
            <option value="season_gdd_grid">Season GDD</option>
            <option value="heat_days_grid">Heat days (Tmax&gt;90°F)</option>
            <option value="freeze_days_grid">Freeze days (Tmin≤28°F)</option>
            <option value="corn_density">🌽 Corn density (where corn grows)</option>
            <option value="soil_nccpi">🏞 Soil productivity (NCCPI)</option>
            <option value="soil_awc">💧 Soil water capacity (AWC)</option>
            <option value="soil_water_stress">🏜️ Soil-water dry-down (this-year)</option>
            <option value="disease_pressure">🍄 Disease pressure (scouting — no yield effect)</option>
          </optgroup>
          <optgroup label="── Radar — actual rain (MRMS, ~1km) ──">
            <option value="radar_p72h">🛰 Radar rain — last 72h</option>
            <option value="radar_p48h">🛰 Radar rain — last 48h</option>
            <option value="radar_p24h">🛰 Radar rain — last 24h</option>
          </optgroup>
          <optgroup label="── Yield + Production ──">
            <option value="yield_hd">🌽 Predicted yield (high-def, sub-county by soil)</option>
            <option value="production_hd">🌽 Production (high-def, corn × soil)</option>
            <option value="production_pct_normal">🌽 Production — % of normal (county)</option>
            <option value="yield_pct_normal_hd">🌽 Yield — % of normal (high-def, this-year)</option>
          </optgroup>
          <optgroup label="── Elevator — supply vs history (pick years) ──">
            <option value="hist_yield">📅 Historical yield — by county/year (RMA final)</option>
            <option value="hist_prod">📅 Historical production — yield × harvested acres</option>
            <option value="yield_vs_cmp">⚖️ 2026 yield vs selected year(s)</option>
            <option value="prod_vs_cmp">⚖️ 2026 production vs selected year(s)</option>
          </optgroup>
          <optgroup label="── Stress + Drought ──">
            <option value="drought_dsci">USDM Drought (DSCI)</option>
            <option value="soil_water_drag">Soil-water drag (county)</option>
          </optgroup>
          <optgroup label="── Composite ──">
            <option value="total_wx">Total weather factor</option>
          </optgroup>
          <optgroup label="── Dairy ──">
            <option value="dairy_cows">Dairy cow inventory (head)</option>
            <option value="silage_yield_est">Silage yield estimate (t/ac)</option>
            <option value="silage_extra">Extra silage acres vs normal year</option>
            <option value="silage_displaced_grain">Corn acres chopped for silage</option>
          </optgroup>
        </select>
        {ELEV_METRICS.includes(metric) && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#3a5a3a" }}>{metric === "hist_yield" || metric === "hist_prod" ? "Year(s):" : "Compare 2026 to:"}</span>
            {[2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015].map((y) => {
              const on = cmpYears.includes(y), rma = y >= 2023;
              return (
                <button key={y} onClick={() => toggleCmpYear(y)} title={rma ? "RMA final yield" : "NASS yield (no RMA final published this year)"} style={{
                  padding: "4px 8px", fontSize: 11, fontWeight: on ? 700 : 500, borderRadius: 6, cursor: "pointer",
                  border: "1px solid " + (on ? "#2d5570" : "#ccd"), background: on ? "#3a6e8a" : "#fff", color: on ? "#fff" : "#3a5a3a",
                }}>{y}<span style={{ fontSize: 9, opacity: 0.7, marginLeft: 2 }}>{rma ? "R" : "N"}</span></button>
              );
            })}
            <span style={{ fontSize: 10, color: "#60735f" }}>{cmpYears.length > 1 ? `avg of ${cmpYears.length} yrs · ` : ""}R=RMA final · N=NASS</span>
          </div>
        )}
        {ELEV_METRICS.includes(metric) && regionSummary && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, padding: "8px 14px", borderRadius: 8, width: "fit-content",
            background: regionSummary.pct != null ? (regionSummary.pct >= 100 ? "#eaf4e6" : "#f6ecd9") : "#eef3ef",
            border: "1px solid " + (regionSummary.pct != null ? (regionSummary.pct >= 100 ? "#bcd9ad" : "#e3cf94") : "#d6e0d6") }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#3a5a3a", textTransform: "uppercase", letterSpacing: 0.4 }}>16-County Total</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: regionSummary.pct != null ? (regionSummary.pct >= 100 ? "#1f7a2e" : "#9a6a23") : "#1a2e1a" }}>{regionSummary.txt}</span>
            <span style={{ fontSize: 11, color: "#5a7258" }}>{regionSummary.sub}</span>
          </div>
        )}
        {(metric === "precip_pct_window" || metric === "precip_in_window") && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            {[["2w", "Last 2 wks"], ["30d", "Last 30 days"], ["may1", "Since May 1"], ["season", "This season"], ["custom", "Custom"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setPrecipWin((w) => ({ ...w, preset: k }))} style={{
                padding: "4px 9px", fontSize: 11, fontWeight: precipWin.preset === k ? 700 : 500, borderRadius: 6, cursor: "pointer",
                border: "1px solid " + (precipWin.preset === k ? "#2d5570" : "#ccd"), background: precipWin.preset === k ? "#3a6e8a" : "#fff", color: precipWin.preset === k ? "#fff" : "#3a5a3a",
              }}>{lbl}</button>
            ))}
            {precipWin.preset === "custom" && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#5a7258" }}>
                <input type="date" value={precipWin.from} onChange={(e) => setPrecipWin((w) => ({ ...w, from: e.target.value }))} style={{ padding: "2px 4px", fontSize: 11, border: "1px solid #ccd", borderRadius: 4 }} />–
                <input type="date" value={precipWin.to} onChange={(e) => setPrecipWin((w) => ({ ...w, to: e.target.value }))} style={{ padding: "2px 4px", fontSize: 11, border: "1px solid #ccd", borderRadius: 4 }} />
              </span>
            )}
            <span style={{ fontSize: 11, color: "#677167" }}>{winDates.from} → {winDates.to} · MRMS radar (gauge-corrected) + gridMET</span>
            {windowSummary && (
              <span style={{ fontSize: 12, color: "#1a2e1a", fontWeight: 600, marginLeft: "auto", background: "#eef3ef", padding: "3px 10px", borderRadius: 6 }}>
                Region avg: <b>{windowSummary.actualIn.toFixed(1)}"</b> received vs <b>{windowSummary.normalIn.toFixed(1)}"</b> normal
                {windowSummary.pct != null && <> · <b style={{ color: windowSummary.pct < 90 ? "#a03028" : windowSummary.pct > 110 ? "#1d5e8c" : "#2f6e2f" }}>{Math.round(windowSummary.pct)}%</b> of normal</>}
              </span>
            )}
          </div>
        )}
        {metric === "forecast_rain" && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ fontSize: 11, color: "#5a7258", fontWeight: 600 }}>Look ahead:</span>
            {[3, 5, 7, 10, 14].map((d) => (
              <button key={d} onClick={() => setFcHorizon(d)} style={{
                padding: "4px 9px", fontSize: 11, fontWeight: fcHorizon === d ? 700 : 500, borderRadius: 6, cursor: "pointer",
                border: "1px solid " + (fcHorizon === d ? "#2d5570" : "#ccd"), background: fcHorizon === d ? "#3a6e8a" : "#fff", color: fcHorizon === d ? "#fff" : "#3a5a3a",
              }}>{d} days</button>
            ))}
            <span style={{ fontSize: 11, color: "#677167" }}>forecast rain, today → +{fcHorizon}d · Open-Meteo</span>
          </div>
        )}
        {M && M.isRadar && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ fontSize: 11, color: "#677167" }}>
              actual rain that fell · our native MRMS gauge-corrected radar, exact 1 km · hover map for value
              {rainGrid && rainGrid.ends ? ` · through ${new Date(rainGrid.ends).toLocaleString([], { month: "short", day: "numeric", hour: "numeric" })}` : ""}
            </span>
          </div>
        )}
        {/* THE GRID FAILED TO LOAD — said plainly, and offered a retry. This is the
            state that used to render as "no 4km grid-cell data here", which is a
            claim about the data rather than about the request. It outranks the
            coverage line below because when it is true, that line is meaningless. */}
        {layerIsCellData && gridLoad === "failed" && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ fontSize: 11.5, color: "#a5461b", fontWeight: 700 }}>
              Couldn&apos;t load the 4&nbsp;km grid — this layer has nothing to draw.
            </span>
            <span style={{ fontSize: 11, color: "#677167" }}>
              The hatching below is NOT a statement that the data is missing.
            </span>
            <button onClick={() => { setGridLoad("loading"); setGridReload((n) => n + 1); }}
              style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, cursor: "pointer",
                       border: "1px solid #d8b9a8", background: "#fff", color: "#a5461b" }}>
              Retry
            </button>
          </div>
        )}
        {anomaliesFailed && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ fontSize: 11.5, color: "#a5461b", fontWeight: 700 }}>
              Couldn&apos;t load the weather grid — a failed read, not an empty grid.
            </span>
            <span style={{ fontSize: 11, color: "#677167" }}>
              Every per-cell surface is drawn from these points, so nothing is shaded.
            </span>
            {/* ⚠ THIS SAID "Reload to try again" WHILE THE MACHINERY TO RETRY WAS
                ALREADY HERE. `setGridReload` re-runs the same fetch and the
                sibling banner six lines up has used it since it was written —
                this one asked for a full page reload instead, which on a phone
                means losing the layer, the region and the draw-area selection.
                Kevin hit the failure on 2026-09-25 and was told to reload.
                The read itself is fine: replayed the same day it returned all
                1,390 NY cells in two pages, ~1.5s each, so what he met was
                transient — which is exactly the case a retry is for. */}
            <button onClick={() => { setGridLoad("loading"); setGridReload((n) => n + 1); }}
              style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, cursor: "pointer",
                       border: "1px solid #d8b9a8", background: "#fff", color: "#a5461b" }}>
              Retry
            </button>
          </div>
        )}
        {layerCoverage.missing > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ display: "inline-block", width: 11, height: 11, borderRadius: 2, background: "repeating-linear-gradient(45deg,#e8e8e4,#e8e8e4 2px,#cfcfc8 2px,#cfcfc8 4px)", border: "1px solid rgba(0,0,0,.12)" }} />
            <span style={{ fontSize: 11, color: "#677167" }}>
              {layerCoverage.has} of {layerCoverage.total} counties carry this layer · {layerCoverage.missing} hatched ={" "}
              {modelMissing
                ? "the county yield model has not finished loading — the hatching is this page waiting, not a statement about these counties"
                : !layerIsCellData
                ? `no county yield model (USDA NASS publishes under ${MI_MIN_YIELD_YEARS} years of county corn grain yield)`
                : gridLoad === "failed" ? "the grid did not load — not a data gap"
                : gridLoad === "loading" ? "still loading the 4km grid…"
                : "no 4km grid-cell data here"}
            </span>
          </div>
        )}
        {/* ⚠ ADMITTING A COUNTY AND TRUSTING IT EQUALLY ARE DIFFERENT DECISIONS.
            Kevin lowered the bar from six NASS grain-yield years to five on
            2026-09-25 so four counties would stop being hatched — they are thin,
            not absent. Without this line the map would assert the same confidence
            for a county resting on five years as for one resting on eleven, which
            is the half of that change that could quietly mislead. Named, and
            derived from `yieldYears` rather than a hand-kept list. */}
        {!layerIsCellData && !modelMissing && thinCountiesShown.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ display: "inline-block", width: 11, height: 11, borderRadius: 2, background: "#fdf6e3", border: "1px solid #d8c48a" }} />
            <span style={{ fontSize: 11, color: "#677167" }}>
              {thinCountiesShown.map((c) => `${c.name} (${c.yieldYears} yrs)`).join(", ")}
              {thinCountiesShown.length === 1 ? " rests" : " rest"} on fewer than {MI_THIN_YIELD_YEARS} years
              of county yield — modelled, but on a thinner record than the rest.
            </span>
          </div>
        )}
        {metric === "corn_density" && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ fontSize: 11, color: "#677167" }}>USDA Cropland Data Layer corn acres — all field corn (grain + silage) — in each map cell (~0.1° ≈ 35 mi² / ~22k ac of land) · darker = more corn · hover for % cover</span>
          </div>
        )}
        {(metric === "production_pct_normal" || metric === "yield_pct_normal_hd") && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ fontSize: 11, color: "#5a7258", fontWeight: 600 }}>Normal =</span>
            {[3, 5, 7, 10].map((y) => (
              <button key={y} onClick={() => setNormalYears(y)} style={{
                padding: "4px 9px", fontSize: 11, fontWeight: normalYears === y ? 700 : 500, borderRadius: 6, cursor: "pointer",
                border: "1px solid " + (normalYears === y ? "#2d6a2d" : "#ccd"), background: normalYears === y ? "#2d6a2d" : "#fff", color: normalYears === y ? "#fff" : "#3a5a3a",
              }}>{y}-yr</button>
            ))}
            <span style={{ fontSize: 11, color: "#677167" }}>
              {metric === "yield_pct_normal_hd"
                ? `county yield vs its ${normalYears}-yr normal, nudged per-cell by THIS year's soil-water dry-down (static soil baseline removed — no sub-county history exists) · tan = below, green = at/above · hover for %`
                : `predicted production ÷ last ${normalYears}-yr avg (county NASS) · tan = short crop, green = big crop · hover for %`}
            </span>
          </div>
        )}
        {metric === "production_hd" && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6, width: "100%" }}>
            <span style={{ fontSize: 11, color: "#677167" }}>Predicted corn-GRAIN bushels per map cell — the county forecast distributed by where corn grows × soil quality (CDL × NCCPI). Per-cell total over ~35 mi² (~22k ac) · darker = more bu · hover for value</span>
          </div>
        )}
        </div>
      </div>
      {/* Held here while the ACTIVE layer’s own data is in flight — see
          cellSurfacePending above. Bounded by gridLoad, so a layer that is
          genuinely empty falls through to the hatch-and-explain path rather than
          sitting on "Loading map…" forever. */}
      {!geoData || !pathGen || cellSurfacePending ? (
        <div style={{ padding: 40, textAlign: "center", color: "#6b7c6b" }}><div style={{ fontSize: 28, marginBottom: 8 }}>🗺</div>Loading map…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 0 }}>
          <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
            {/* An empty per-cell layer must SAY it is empty. This map spent three
                days drawing outlines with a "No data" legend and no explanation,
                which reads as "there is no dry-down" rather than "the numbers
                were never fetched". */}
            {(cellLayersPending || cellLayersMissing) && (
              <div style={{ position: "absolute", top: 10, left: 10, zIndex: 5, fontSize: 11.5, fontWeight: 700,
                            color: cellLayersMissing ? "#8a6d1f" : "#31572c", background: cellLayersMissing ? "#fff6e6" : "#eef6ea",
                            border: "1px solid " + (cellLayersMissing ? "#e8d39a" : "#cfe0cf"), borderRadius: 6, padding: "4px 9px" }}>
                {cellLayersPending
                  ? "Loading per-cell layer…"
                  : "Per-cell layer not stored yet — it lands on the next model run."}
              </div>
            )}
            <svg ref={mapSvgRef} viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`} onMouseDown={onMapDown} onMouseMove={onSvgMouseMove} onMouseUp={onMapUp} onMouseLeave={onSvgLeave} onDoubleClick={resetView} style={{ width: "100%", height: "auto", display: "block", background: "#dae5ee", cursor: dragRef.current ? "grabbing" : "grab", touchAction: "none" }}>
              <defs>
                <pattern id="noDataMask" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)">
                  <rect width="7" height="7" fill="#e3e7e3" />
                  <line x1="0" y1="0" x2="0" y2="7" stroke="#b9c2b9" strokeWidth="2.5" />
                </pattern>
                {/* Clip everything to the 16-county outline so the interpolated
                    surface never bleeds into the lake / Pennsylvania / off-area. */}
                <clipPath id="wnyClip">
                  {geoData.features.map((f) => { const d = pathGen(f); return d ? <path key={"clip" + f.id} d={d} /> : null; })}
                </clipPath>
              </defs>
              {/* CHOROPLETH SURFACE — smooth IDW-interpolated heatmap, clipped to WNY */}
              {surfaceContours && surfaceContours.length > 0 && (
                <g style={{ pointerEvents: "none" }} clipPath="url(#wnyClip)">
                  {surfaceContours.map((c, i) => (
                    <path key={"surf" + i} d={c.path}
                      fill={scaleColor(M.scale, c.value, M.domain)}
                      stroke="none" opacity={0.92} />
                  ))}
                </g>
              )}
              {/* LIVE MRMS RADAR IMAGE — real-time ~1km radar QPE, clipped to WNY.
                  County outlines + lakes draw on top for orientation. */}
              {radarOverlay && (
                <image href={radarOverlay.url} xlinkHref={radarOverlay.url}
                  x={radarOverlay.x} y={radarOverlay.y} width={radarOverlay.w} height={radarOverlay.h}
                  preserveAspectRatio="none" opacity={0.82} clipPath="url(#wnyClip)"
                  style={{ pointerEvents: "none" }} />
              )}
              {/* Corn-density mute — fade the surface toward the base where little
                  corn grows (cities/forest), so the metric only shows where it matters. */}
              {cornMaskCells && cornMaskCells.length > 0 && (
                <g style={{ pointerEvents: "none" }} clipPath="url(#wnyClip)">
                  {cornMaskCells.map((c, i) => (
                    <polygon key={"cmask" + i} points={c.pts} fill="#eaf0ea" opacity={c.alpha} stroke="none" />
                  ))}
                </g>
              )}
              {/* Finger Lakes — water over the choropleth so the glacial lakes read
                  as water, not interpolated corn yield. Unclipped (they straddle
                  county lines); the Great Lakes are already the blue background. */}
              <g style={{ pointerEvents: "none" }}>
                {FINGER_LAKES.features.map((lk) => {
                  const d = pathGen(lk); if (!d) return null;
                  return <path key={"lake" + lk.properties.name} d={d} fill="#a9cbe6" stroke="#8fb4d4" strokeWidth={0.4} opacity={0.96} />;
                })}
              </g>
              {geoData.features.map((f) => {
                const nm = f.properties.name, d = pathGen(f); if (!d) return null;
                // Selection is held as FIPS, not name: the map now draws counties
                // the model has no row for, and their TopoJSON spelling is the
                // only name available for them.
                const isH = hovered === nm, isS = selected === f.properties.fips;
                // If a surface is rendered, keep county fill transparent so the
                // surface shows through; outline gets stronger for orientation.
                const hasSurface = !!(surfaceContours && surfaceContours.length > 0);
                // Radar image overlay also wants transparent county fills + crisp outlines.
                const overlay = hasSurface || !!radarOverlay;
                // Mask un-loaded counties for CDL-dependent metrics so the surface's
                // interpolation can't bleed misleading corn across the data gap.
                const cov = cdlCoverageByFips[f.properties.fips];
                const thinCorn = M.needsCorn && hasSurface && (cov == null || cov < 0.7);
                // A county the ACTIVE layer has no data for is hatched, not left
                // off the map. The hatch paints over the surface, so this is also
                // what keeps a contour from bleeding a number into ground the
                // layer was never fit on — the job `thinCorn` already did for CDL.
                const masked = thinCorn || !layerHasData(f.properties.fips);
                const baseFill = masked ? "url(#noDataMask)" : overlay ? "transparent" : getFill(f.properties.fips);
                const selFill = overlay ? "rgba(255,243,176,0.5)" : "#fff3b0";
                const hovFill = overlay ? "rgba(255,224,130,0.5)" : "#ffe082";
                const inCatch = !!(cat && cat.has && cat.has(nm.toUpperCase()));
                const fill = isS ? selFill : isH ? hovFill : (inCatch ? "rgba(21,89,163,0.20)" : baseFill);
                const strokeC = isS ? "#a67c00" : isH ? "#1a2e1a" : inCatch ? "#1559a3" : overlay ? "#1a2e1a" : "#8fa88f";
                const strokeW = isS ? 2.4 : isH ? 1.6 : inCatch ? 2.4 : overlay ? 1.1 : 0.6;
                return (
                  <path key={f.id} d={d} fill={fill}
                    stroke={strokeC}
                    strokeWidth={strokeW}
                    strokeLinejoin="round"
                    style={{ cursor: drawMode ? "crosshair" : "pointer", transition: "fill 0.15s" }}
                    onMouseEnter={() => !isMobile && setHovered(nm)}
                    onMouseLeave={() => !isMobile && setHovered(null)}
                    onClick={() => { if (suppressClickRef.current) { suppressClickRef.current = false; return; } if (drawMode) { toggleCatch(nm.toUpperCase()); } else { setSelected(isS ? null : f.properties.fips); } }}
                  />
                );
              })}
              {/* PRF rain grids — 0.25° squares. Outliers (rainfall < 30% of
                  neighbor median when neighbors are wet) get a diagonal-stripe
                  pattern to flag bad data instead of being colored as if real.
                  Hover/click any grid for the full data card. */}
              {showPrfGrids && prfGridShapes.length > 0 && (
                <g>
                  <defs>
                    <pattern id="prfOutlier" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                      <rect width="8" height="8" fill="rgba(255,170,80,0.45)" />
                      <line x1="0" y1="0" x2="0" y2="8" stroke="#cc4400" strokeWidth="2" />
                    </pattern>
                  </defs>
                  {prfGridShapes.map((s) => {
                    const isH = hoveredGrid === s.grid_id;
                    return (
                      <polygon key={s.grid_id} points={s.pts}
                        fill={s.is_outlier ? "url(#prfOutlier)" : prfRainColor(s.pct, s.rain)}
                        stroke={isH ? "#1a2e1a" : s.is_outlier ? "#cc4400" : "rgba(0,0,0,0.18)"}
                        strokeWidth={isH ? 1.8 : s.is_outlier ? 1.0 : 0.4}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => !isMobile && setHoveredGrid(s.grid_id)}
                        onMouseLeave={() => !isMobile && setHoveredGrid((g) => g === s.grid_id ? null : g)}
                        onClick={(e) => { e.stopPropagation(); setHoveredGrid((g) => g === s.grid_id ? null : s.grid_id); }}
                      />
                    );
                  })}
                  {/* Grid value labels — only show if grid is big enough on screen */}
                  {prfGridShapes.map((s) => (
                    <text key={"gl" + s.grid_id} x={s.cx} y={s.cy} textAnchor="middle" dominantBaseline="central"
                      fontSize={7.5} fontWeight={700} fill={s.is_outlier ? "#7a3000" : "#1a1a1a"}
                      style={{ paintOrder: "stroke", stroke: "#ffffff", strokeWidth: 1.5, pointerEvents: "none" }}>
                      {s.is_outlier ? "?" : (s.rain != null ? s.rain.toFixed(1) + '"' : "")}
                    </text>
                  ))}
                  {/* Hover tooltip for grids */}
                  {hoveredGrid != null && (() => {
                    const s = prfGridShapes.find((x) => x.grid_id === hoveredGrid);
                    if (!s) return null;
                    const lines = [
                      `Grid #${s.grid_id} · ${s.lat.toFixed(3)}, ${s.lon.toFixed(3)}`,
                      s.is_outlier ? `⚠ Suspected bad data` : null,
                      `Rainfall: ${s.rain != null ? s.rain.toFixed(2) + '"' : "—"}`,
                      s.normal != null ? `Normal: ${s.normal.toFixed(2)}"` : `Normal: not published yet`,
                      s.pct != null ? `% of normal: ${Math.round(s.pct * 100)}%` : null,
                      s.is_outlier && s.neighbor_median != null ? `Neighbor median: ${s.neighbor_median.toFixed(1)}" (this grid is the outlier)` : null,
                    ].filter(Boolean);
                    const bw = 230, bh = 16 + lines.length * 13;
                    let tx = s.cx, ty = s.cy - bh - 14;
                    // Clamp to viewbox
                    if (tx - bw / 2 < 5) tx = bw / 2 + 5;
                    if (tx + bw / 2 > 795) tx = 795 - bw / 2;
                    if (ty < 5) ty = s.cy + 16;
                    return (
                      <g style={{ pointerEvents: "none" }}>
                        <rect x={tx - bw / 2} y={ty} width={bw} height={bh} rx={6} fill="rgba(26,46,26,.96)" stroke={s.is_outlier ? "#ff8844" : "none"} strokeWidth={s.is_outlier ? 1.5 : 0} />
                        {lines.map((l, i) => (
                          <text key={i} x={tx} y={ty + 14 + i * 13} textAnchor="middle" fontSize="10" fill={i === 1 && s.is_outlier ? "#ffb070" : i === 0 ? "#fff" : "#c8dfc8"} fontWeight={i === 0 ? 700 : 500}>{l}</text>
                        ))}
                      </g>
                    );
                  })()}
                </g>
              )}

              {/* WNY weather-grid cells — 389 Daymet points, colored by county.
                  Native <title> tooltip shows county + lat/lon for spot-checks. */}
              {showWnyGrid && wnyGridDots.length > 0 && (
                <g>
                  {wnyGridDots.map((d, i) => (
                    <circle key={"wny" + i} cx={d.x} cy={d.y} r={d.val != null ? 3.4 : 2.6}
                      fill={d.fill || wnyCountyColor(d.county)} stroke="#0a1e0a" strokeWidth={0.5} opacity={0.96}>
                      <title>{d.county} · {d.lat.toFixed(2)}, {d.lon.toFixed(2)}{d.val != null && d.fmt ? ` · ${d.fmt(d.val)}` : ""}</title>
                    </circle>
                  ))}
                </g>
              )}

              {/* Customer farm pins — in-area corn farms only. Bigger amber dots
                  on top of grid + county fills so the book is visually dominant. */}
              {showFarms && farmDots.length > 0 && (
                <g>
                  {farmDots.map((d, i) => (
                    <circle key={"farm" + i} cx={d.x} cy={d.y} r={4.2}
                      fill="#f59e0b" stroke="#1a2e1a" strokeWidth={0.9} opacity={0.96}>
                      <title>{d.name + (d.counties?.length ? " · " + d.counties.join(", ") : "")}</title>
                    </circle>
                  ))}
                </g>
              )}

              {/* Town (civil township) outlines — decorative (pointerEvents none);
                  the hovered town's name is computed in onSvgMouseMove and labeled
                  at its centroid so the map never fills with text. */}
              {showTowns && townFeatures && townFeatures.length > 0 && pathGen && (
                <g style={{ pointerEvents: "none" }} clipPath="url(#wnyClip)">
                  {townFeatures.map((f, i) => { const d = pathGen(f); if (!d) return null; const isH = hoveredTown && hoveredTown.name === f.properties.NAME; return (
                    <path key={"twn" + i} d={d} fill="none" stroke={isH ? "#1a2e1a" : "#5a6b5a"} strokeWidth={isH ? 1.3 : 0.5} strokeOpacity={isH ? 0.95 : 0.5} />
                  ); })}
                </g>
              )}
              {/* County labels with metric values */}
              {geoData.features.map((f) => {
                const nm = f.properties.name;
                const c = centroids[nm]; if (!c) return null;
                const area = pathGen.area(f); if (area < 60) return null;
                const cty = byFips[String(f.properties.fips)];
                const val = cty && typeof M.getter === "function" ? M.getter(cty) : null;
                const fs = area > 3000 ? 9 : area > 1500 ? 7.5 : area > 500 ? 6.5 : 5.5;
                return (
                  <g key={"lbl" + f.id} style={{ pointerEvents: "none" }}>
                    <text x={c[0]} y={c[1] - 4} textAnchor="middle" dominantBaseline="central"
                      fontSize={fs} fontWeight={600} fill="#1a2e1a"
                      style={{ paintOrder: "stroke", stroke: "#ffffff", strokeWidth: 2 }}>
                      {nm.length > 9 ? nm.slice(0, 8) + "…" : nm}
                    </text>
                    {val != null && (
                      <text x={c[0]} y={c[1] + fs + 1} textAnchor="middle" dominantBaseline="central"
                        fontSize={Math.max(fs - 0.5, 5)} fontWeight={800} fill="#0a1e0a"
                        style={{ paintOrder: "stroke", stroke: "#ffffff", strokeWidth: 2 }}>
                        {M.fmt(val).replace(" bu/ac", "").replace(" bu", "").replace("DSCI ", "")}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* Hover tooltip */}
              {/* Hover only highlights; click a county for the full detail panel below. */}
              {/* RADAR hover readout — rain value (inches) under the cursor, read from the
                  live radar image via the MRMS Q3 color scale. */}
              {radarTip && (() => {
                const v = radarTip.val;
                const label = v >= 14.5 ? "≥15\"" : `≈ ${v < 1 ? v.toFixed(2) : v.toFixed(1)}"`;
                const bw = 70, bh = 22;
                let bx = radarTip.x + 9, by = radarTip.y - bh - 6;
                if (bx + bw > view.x + view.w) bx = radarTip.x - bw - 9;
                if (by < view.y) by = radarTip.y + 9;
                return (
                  <g style={{ pointerEvents: "none" }}>
                    <circle cx={radarTip.x} cy={radarTip.y} r={2.6} fill="#fff" stroke="#1a2e1a" strokeWidth={1} />
                    <rect x={bx} y={by} width={bw} height={bh} rx={5} fill="rgba(26,46,26,.95)" />
                    <text x={bx + bw / 2} y={by + bh / 2 + 0.5} textAnchor="middle" dominantBaseline="central" fontSize={13} fontWeight={800} fill="#fff">{label}</text>
                  </g>
                );
              })()}
              {/* Hovered grid-cell footprint — the actual 0.1°×0.1° tile the value
                  belongs to, drawn only while hovering a high-def metric so the size
                  of the area is tangible. Disappears on mouse-leave (hoverTip null). */}
              {hoverTip && hoverTip.cell && !radarTip && projection && (() => {
                const { lat, lon } = hoverTip.cell;
                if (lat == null || lon == null) return null;
                const h = 0.05;
                const pts = [[lon - h, lat + h], [lon + h, lat + h], [lon + h, lat - h], [lon - h, lat - h]]
                  .map(([lo, la]) => projection([lo, la]));
                if (pts.some((p) => !p || isNaN(p[0]))) return null;
                return (
                  <polygon points={pts.map((p) => `${p[0]},${p[1]}`).join(" ")}
                    fill="rgba(255,255,255,0.22)" stroke="#1a2e1a" strokeWidth={1.3} strokeDasharray="3 2"
                    style={{ pointerEvents: "none" }} />
                );
              })()}
              {/* Choropleth value readout — the metric value under the cursor (yield,
                  soil AWC, dry-down, rainfall, etc.). Mirrors the radar tooltip. */}
              {hoverTip && !radarTip && (() => {
                const label = M.fmt(hoverTip.val);
                if (label == null || label === "" || label === "—") return null;
                const sub = (M.hoverSub && hoverTip.cell) ? M.hoverSub(hoverTip.val, hoverTip.cell) : null;
                const z = view.w / 800; // counter-scale so the pill stays a constant on-screen size at any zoom
                const widest = Math.max(String(label).length * 7, sub ? String(sub).length * 5.4 : 0);
                const bw = Math.max(48, widest + 16) * z, bh = (sub ? 36 : 22) * z;
                let bx = hoverTip.x + 9 * z, by = hoverTip.y - bh - 6 * z;
                if (bx + bw > view.x + view.w) bx = hoverTip.x - bw - 9 * z;
                if (by < view.y) by = hoverTip.y + 9 * z;
                return (
                  <g style={{ pointerEvents: "none" }}>
                    <circle cx={hoverTip.x} cy={hoverTip.y} r={2.6 * z} fill="#fff" stroke="#1a2e1a" strokeWidth={z} />
                    <rect x={bx} y={by} width={bw} height={bh} rx={5 * z} fill="rgba(26,46,26,.95)" />
                    <text x={bx + bw / 2} y={by + (sub ? 13 * z : bh / 2 + 0.5 * z)} textAnchor="middle" dominantBaseline="central" fontSize={12 * z} fontWeight={800} fill="#fff">{label}</text>
                    {sub && <text x={bx + bw / 2} y={by + 26 * z} textAnchor="middle" dominantBaseline="central" fontSize={9 * z} fontWeight={600} fill="#bcd4bc">{sub}</text>}
                  </g>
                );
              })()}
              {/* Town name — its own small pill BELOW the cursor, so it shows
                  alongside the metric value (which sits above the cursor) rather
                  than competing with it. Only when Town lines is on. */}
              {showTowns && hoveredTown && (() => {
                const z = view.w / 800; // keep the town pill a constant on-screen size at any zoom
                const lbl = hoveredTown.name || "";
                const bw = Math.max(42, lbl.length * 5.6 + 12) * z, bh = 16 * z;
                let bx = hoveredTown.x - bw / 2, by = hoveredTown.y + 12 * z;
                if (bx < view.x + 2) bx = view.x + 2;
                if (bx + bw > view.x + view.w - 2) bx = view.x + view.w - bw - 2;
                if (by + bh > view.y + view.h) by = hoveredTown.y - bh - 12 * z;
                return (
                  <g style={{ pointerEvents: "none" }}>
                    <rect x={bx} y={by} width={bw} height={bh} rx={4 * z} fill="rgba(40,52,40,.9)" />
                    <text x={bx + bw / 2} y={by + bh / 2 + 0.5 * z} textAnchor="middle" dominantBaseline="central" fontSize={9 * z} fontWeight={700} fill="#fff">{lbl}</text>
                  </g>
                );
              })()}
            </svg>
            {/* Legend */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "6px 12px", borderTop: "1px solid #eee", justifyContent: "center" }}>
              {legendBuckets.map((it, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#5a6b5a" }}>
                  <div style={{ width: 11, height: 11, borderRadius: 2, background: it.c, border: "1px solid rgba(0,0,0,.1)" }} />
                  {it.l}
                </div>
              ))}
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#aaa" }}><div style={{ width: 11, height: 11, borderRadius: 2, background: "#f2f2f0", border: "1px solid rgba(0,0,0,.1)" }} />No data</div>
            </div>
          </div>
          {/* Selected county the yield model has no row for. Says WHY, and says
              what the county does have, rather than opening an empty panel. */}
          {selectedGap && (
            <div style={{ width: isMobile ? "100%" : 240, padding: 14, borderLeft: isMobile ? "none" : "1px solid #e4ece4", borderTop: isMobile ? "1px solid #e4ece4" : "none", background: "#fafcfa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontWeight: 800, color: "#1a2e1a", fontSize: 13 }}>{selectedGap.name}</span>
                <button onClick={() => setSelected(null)} style={{ background: "transparent", border: "none", color: "#888", cursor: "pointer", fontSize: 14 }}>✕</button>
              </div>
              <div style={{ fontSize: 11.5, color: "#5a7a5a", lineHeight: 1.5 }}>
                {selectedGap.cells > 0 ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, color: "#1a2e1a" }}>{Math.round(selectedGap.corn).toLocaleString()} ac</span> of CDL corn across{" "}
                      <span style={{ fontWeight: 700, color: "#1a2e1a" }}>{selectedGap.cells}</span> grid cells — the corn, soil, rainfall
                      and GDD layers all cover this county.
                    </div>
                    <div>
                      No yield or production number, because USDA NASS has published fewer than 6 years of county
                      corn <b>grain</b> yield here. Silage counties fail that test on grain even when they grow a lot of corn.
                    </div>
                  </>
                ) : (
                  <div>No 4km grid cells built for this county, so every per-cell layer reads no data here.</div>
                )}
              </div>
            </div>
          )}
          {/* Selected county sidebar */}
          {selectedCty && (
            <div style={{ width: isMobile ? "100%" : 240, padding: 14, borderLeft: isMobile ? "none" : "1px solid #e4ece4", borderTop: isMobile ? "1px solid #e4ece4" : "none", background: "#fafcfa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontWeight: 800, color: "#1a2e1a", fontSize: 13 }}>{selectedCty.name}</span>
                <button onClick={() => setSelected(null)} style={{ background: "transparent", border: "none", color: "#888", cursor: "pointer", fontSize: 14 }}>✕</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11.5 }}>
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Yield</div><div style={{ fontWeight: 800, color: "#2d6a2d", fontSize: 14 }}>{selectedCty.forecast_yield} bu/ac</div></div>
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>5-yr avg</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>{selectedCty.recent_5yr_mean}</div></div>
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Production</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>{selectedCty.pred_prod != null ? `${(selectedCty.pred_prod / 1e6).toFixed(2)}M bu` : "—"}</div></div>
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Pred acres</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>{selectedCty.pred_acres ? selectedCty.pred_acres.toLocaleString() : "—"}</div></div>
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Rain</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>{selectedCty.weather?.season_precip_in}" {selectedCty.weather?.precip_anom != null ? ` (${selectedCty.weather.precip_anom > 0 ? "+" : ""}${Math.round(selectedCty.weather.precip_anom * 100)}%)` : ""}</div></div>
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>GDD</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>{selectedCty.weather?.season_gdd}{selectedCty.weather?.gdd_anom != null ? ` (${selectedCty.weather.gdd_anom > 0 ? "+" : ""}${Math.round(selectedCty.weather.gdd_anom * 100)}%)` : ""}</div></div>
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>DSCI</div><div style={{ fontWeight: 700, color: selectedCty.drought?.dsci >= 100 ? "#b85000" : selectedCty.drought?.dsci >= 50 ? "#b08900" : "#3f7d3f" }}>{selectedCty.drought?.dsci ?? 0} {selectedCty.drought?.category ? `· ${selectedCty.drought.category}` : ""}</div></div>
                {/* Soil-water drag (the bucket) is the component that feeds Wx total now;
                    falls back to the legacy cold/wet early-stress only when the grid bucket
                    is unavailable. So Wx total ≈ (1 − soil-water) × temp, and the two reconcile. */}
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>{selectedCty.water_balance ? "Soil-water" : "Stress drag"}</div><div style={{ fontWeight: 700, color: (selectedCty.water_balance ? selectedCty.water_balance.drag_pct : selectedCty.early_stress?.drag_pct) >= 4 ? "#b85000" : "#1a2e1a" }}>−{(selectedCty.water_balance ? selectedCty.water_balance.drag_pct : selectedCty.early_stress?.drag_pct) ?? 0}%</div></div>
                {selectedCty.wF_grid != null && <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Temp factor</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>×{selectedCty.wF_grid}</div></div>}
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Wx total</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>×{selectedCty.total_wx_factor ?? 1}</div></div>
                <div><div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Pace</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>×{selectedCty.pace_factor}</div></div>
                {selectedCty.dairy_cows != null && (
                  <div title="NASS county milk-cow survey estimate — undercounts counties with a few large dairies (e.g. Orleans). The silage model does NOT rely on this absolute; demand is anchored to NASS silage ACRES, and the cow figure only sets a year-over-year growth ratio.">
                    <div style={{ color: "#60735f", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Dairy cows · NASS est{selectedCty.dairy_cows_year ? ` (${selectedCty.dairy_cows_year})` : ""}</div>
                    <div style={{ fontWeight: 700, color: "#1a4a7a" }}>{selectedCty.dairy_cows.toLocaleString()} head</div>
                  </div>
                )}
              </div>
              {selectedCty.silage && (
                <div style={{ marginTop: 12, padding: "10px 12px", background: "#fff", border: "1px solid #d6e0d6", borderRadius: 8 }}>
                  <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: "#5a7a5a", fontWeight: 700, marginBottom: 6 }}>🌽 Silage demand chain</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11.5 }}>
                    <div><div style={{ color: "#60735f", fontSize: 10.5 }}>Hist silage ac (3-yr)</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>{selectedCty.silage.historical_silage_ac_3yr_avg != null ? selectedCty.silage.historical_silage_ac_3yr_avg.toLocaleString() : "—"}</div></div>
                    <div><div style={{ color: "#60735f", fontSize: 10.5 }}>Herd vs 3-yr avg</div><div style={{ fontWeight: 700, color: "#1a2e1a" }}>{selectedCty.silage.cow_change_factor != null ? `×${selectedCty.silage.cow_change_factor}` : "—"}</div></div>
                    <div><div style={{ color: "#60735f", fontSize: 10.5 }}>Yield est (t/ac)</div><div style={{ fontWeight: 700, color: "#3a6e3a" }}>{selectedCty.silage.yield_est_t_per_ac}<span style={{ color: "#999", fontWeight: 500, fontSize: 10 }}> vs {selectedCty.silage.yield_normal_t_per_ac} normal</span></div></div>
                    <div><div style={{ color: "#60735f", fontSize: 10.5 }}>Stress factor</div><div style={{ fontWeight: 700, color: miDeltaColor((selectedCty.silage.stress_factor || 1) - 1) }}>×{selectedCty.silage.stress_factor}</div></div>
                    <div><div style={{ color: "#60735f", fontSize: 10.5 }}>Corn ac for silage</div><div style={{ fontWeight: 800, color: "#1a2e1a" }}>{selectedCty.silage.acres_needed ? selectedCty.silage.acres_needed.toLocaleString() : "—"}</div></div>
                    <div><div style={{ color: "#60735f", fontSize: 10.5 }}>Normal-yr acres</div><div style={{ fontWeight: 700, color: "#5a7a5a" }}>{selectedCty.silage.acres_normal_year ? selectedCty.silage.acres_normal_year.toLocaleString() : "—"}</div></div>
                    <div style={{ gridColumn: "1 / -1", paddingTop: 4, borderTop: "1px solid #eef3ee" }}>
                      <div style={{ color: "#60735f", fontSize: 10.5 }}>Extra acres dairies need (vs normal year)</div>
                      <div style={{ fontWeight: 800, color: selectedCty.silage.extra_acres_vs_normal > 1000 ? "#b85000" : selectedCty.silage.extra_acres_vs_normal > 0 ? "#b08900" : "#3f7d3f", fontSize: 14 }}>{selectedCty.silage.extra_acres_vs_normal != null ? `${selectedCty.silage.extra_acres_vs_normal >= 0 ? "+" : ""}${selectedCty.silage.extra_acres_vs_normal.toLocaleString()} ac` : "—"}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Elevator draw-area supply readout — driven by the on-map county picker */}
      {drawSummary && (
        <div style={{ borderTop: "1px solid #e3ecf3", background: "#f3f8fc", padding: "12px 16px" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            <span style={{ fontWeight: 800, color: "#15406e" }}>🏭 Draw-area supply — {drawSummary.isAll ? `all ${drawSummary.total} counties` : `${drawSummary.count} of ${drawSummary.total} counties`}</span>
            <span style={{ fontSize: 10.5, color: "#5a7a9a" }}>{drawMode ? "click counties to add / remove" : "toggle 🏭 Draw area above to edit"}</span>
          </div>
          <div style={{ display: "flex", gap: "10px 30px", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4, color: "#5a7a9a" }}>Predicted yield</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#15406e", lineHeight: 1.05 }}>{drawSummary.yieldWavg != null ? Math.round(drawSummary.yieldWavg) : "—"}<span style={{ fontSize: 12, color: "#7a9bb5", fontWeight: 600 }}> bu/ac</span></div>
            </div>
            <div>
              <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4, color: "#5a7a9a" }}>Est. corn-grain production</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#15406e", lineHeight: 1.05 }}>{drawSummary.prod > 0 ? `${(drawSummary.prod / 1e6).toFixed(1)}M` : "—"}<span style={{ fontSize: 12, color: "#7a9bb5", fontWeight: 600 }}> bu</span></div>
              {drawSummary.vsNorm != null && <div style={{ fontSize: 11, fontWeight: 700, color: drawSummary.vsNorm >= 0 ? "#2f7d32" : "#b85000" }}>{drawSummary.vsNorm >= 0 ? "▲" : "▼"} {Math.abs(Math.round(drawSummary.vsNorm * 100))}% vs 5-yr ({(drawSummary.norm / 1e6).toFixed(1)}M)</div>}
            </div>
            {drawSummary.acres > 0 && (
              <div>
                <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4, color: "#5a7a9a" }}>Grain-corn acres</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#1a2e1a", lineHeight: 1.1 }}>{Math.round(drawSummary.acres).toLocaleString()}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4, color: "#5a7a9a" }}>Harvest window</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#2d6a2d", lineHeight: 1.2 }}>~{drawSummary.harvest.fmt(drawSummary.harvest.h0)} – {drawSummary.harvest.fmt(drawSummary.harvest.h1)}</div>
              <div style={{ fontSize: 10.5, color: "#60735f" }}>black layer ~{drawSummary.harvest.fmt(drawSummary.harvest.bl)} · GDD {drawSummary.harvest.gddAnom >= 0 ? "+" : ""}{Math.round(drawSummary.harvest.gddAnom * 100)}% vs normal</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// One sentence explaining why a county carries the ⚠ badge. Same wording on the
// row, the banner and the email brief, so the caveat reads identically wherever
// somebody meets the number. The flag itself is computed in the model (see the
// `overreach` block in src/miYieldModel.js) precisely so the page and the cron
// cannot disagree about which counties are flagged.
// Why a wet county's number came DOWN. The taper must never be silent: it is the
// one place the model deliberately departs from what its own fitted curve says,
// so the reason travels with the number.
// Where this county's rainfall sits relative to the range our record covers.
// Reports only — it changes no number. Over 144 WNY county-years yield RISES
// monotonically with rain right through the wettest bin (480mm+ is the best group
// at 1.12x its anchor), so the data gives us no basis to mark this county down.
// It does mean the forecast is extrapolating past the last observation.
function miWetNote(c) {
  if (c == null || c.wet_z == null) return "";
  return `${c.name} is at ${c.wet_z.toFixed(2)}σ for May–Aug rain. The wettest county-year in our record is +2.29σ (Cattaraugus 2021, which yielded WELL), so this is past anything we have observed — the forecast here is extrapolating. It is not marked down: across 144 county-years yield rises with rain all the way through the wettest bin, so we have no measured basis for a penalty. The upper edge of the curve is genuinely unlocated — it needs a sample that contains wetter ground than WNY has ever been.`;
}

// Turn ISO date strings back into Date objects after a JSON round-trip.
//
// miReviveDates moved to src/miYieldModel.js — api/_mi-brief.js rebuilds the
// Western NY view from the stored result and hits the same flattened dates, so
// the revival rule now sits beside the model instead of only in this file.
function miOverreachNote(c) {
  const o = c && c.overreach;
  if (!o) return "";
  const bits = [];
  if (o.pegged) bits.push(`weather factor at the ${o.ceil} ceiling${o.index_ratio ? ` (raw index ${o.index_ratio}× normal)` : ""}`);
  if (o.above_record) bits.push(`${o.over_by} bu over its ${o.record_year} record of ${o.record}${o.record_basis === "rma_final" ? " (RMA final, insured-acre — the basis this forecast uses)" : " (NASS all-acre)"}`);
  return bits.join("; ");
}

// ── Region filter ────────────────────────────────────────────────────────────
//
// MI_REGIONS + miFilterResult now live in src/miYieldModel.js and are imported
// at the top of this file. They moved because api/_mi-snapshot.js writes the
// SAME tables this page writes and needs the identical filter to produce the
// Western NY row — a second copy there would have been the usual drift.

// `isPortal` is a SECURITY boundary here, not a layout flag. The raw path below
// reads planting_survey_farmers_public / planting_intentions_public /
// planting_progress_public — three SECURITY DEFINER views that published EVERY
// surveyed grower's intentions to any authenticated session (2026-08-14 audit,
// finding H-1). Those views are now revoked from `authenticated`, so a portal
// session physically cannot read them; this prop is what stops the portal from
// TRYING and silently degrading to a model with no planting-pace cohorts — the
// 157-vs-168 bu split that created these views in the first place.
//
// The portal is snapshot-only: it renders mi_model_snapshot, the row the 07:00
// and 14:30 crons write with the identical model code. That makes portal and
// agent output identical BY CONSTRUCTION rather than by re-deriving it twice.
function MIYieldView({ isPortal = false }) {
  const nowYear = new Date().getFullYear();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [year, setYear] = useState(nowYear);
  const [loading, setLoading] = useState(true);
  const [dataRaw, setDataRaw] = useState(null);
  const [err, setErr] = useState(null);
  const [ovr, setOvr] = useState({}); // fips -> string factor
  const [sortKey, setSortKey] = useState("forecast_yield");
  const [expanded, setExpanded] = useState(null);   // fips of expanded county row
  const [showCounties, setShowCounties] = useState(true);
  const [showLab, setShowLab] = useState(false);     // Model Lab collapsed by default
  const [signalsRefreshing, setSignalsRefreshing] = useState(false);
  const [signalsRefreshMsg, setSignalsRefreshMsg] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryStatus, setSummaryStatus] = useState("");
  const [summaryGenAt, setSummaryGenAt] = useState(null);
  // Mid-week nowcast outputs (Corn) — surfaced on MI for the agent-side view
  // since planting pace is the dominant near-term yield driver. Computed
  // from rawRef during recompute, reset to null when there's no signal.
  const [cornNowcast, setCornNowcast] = useState(null);
  const [cornBacktest, setCornBacktest] = useState(null);
  const [cornSurveyPoints, setCornSurveyPoints] = useState([]);  // [{date:'M/D', pct, totalPlanted}] — full survey history for the chart
  const [cornStaleAdj, setCornStaleAdj] = useState(null);        // stale-reporting adjustment summary
  const rawRef = React.useRef(null);                 // cached Supabase rows (fetch once)
  const [lab, setLab] = useState(miDefaultLab);      // tunable scenario config
  const [baseDataRaw, setBaseDataRaw] = useState(null);    // default-config result (for side-by-side)
  // Region filter. Every render site below reads the FILTERED data, so the hero,
  // the county table, the map and the PDF are physically incapable of disagreeing
  // about which counties they are describing.
  // DEFAULT IS THE BOOK. Kevin, 2026-08-15: open on Western NY, toggle out to all
  // of New York. The 16 are the ground the agency actually writes on, so that is
  // the number someone opening this page is there to read; the other 23 counties
  // are context, and context should be asked for rather than assumed. (The stored
  // preference still wins — this only decides the first visit.)
  const [region, setRegion] = useState(() => { try { return sessionStorage.getItem("miYieldRegion") || "wny"; } catch (_) { return "wny"; } });
  useEffect(() => { try { sessionStorage.setItem("miYieldRegion", region); } catch (_) {} }, [region]);
  const data = useMemo(() => miFilterResult(dataRaw, region), [dataRaw, region]);
  const baseData = useMemo(() => miFilterResult(baseDataRaw, region), [baseDataRaw, region]);
  const [gridShadow, setGridShadow] = useState(null);// Phase 2 per-cell gridMET shadow weather factor (display-only)
  // ── PER-CELL MAP LAYERS ON THE FAST PATH ────────────────────────────────────
  // `gridShadow` is built ONLY by load(), and load() runs only for the Model Lab.
  // Since the page started reading the precomputed snapshot (2026-08-13) that
  // means the two layers which need per-cell values — soil-water dry-down and
  // disease pressure — rendered empty on every normal visit: outlines drawn,
  // legend reading "No data". The raw path would fix it and costs ~662k rows;
  // the same numbers are 220 KB in mi_model_snapshot.map_layers, fetched only
  // when one of those two layers is actually picked.
  const [mapLayers, setMapLayers] = useState(null);
  const [mapLayersState, setMapLayersState] = useState("idle"); // idle | loading | ready | empty
  const ensureCellLayers = useCallback(async () => {
    if (gridShadow || mapLayersState !== "idle") return;   // raw path already has them
    setMapLayersState("loading");
    try {
      const rows = await sbFetch("mi_model_snapshot", { select: "map_layers", filters: [`crop_year=eq.${year}`], fresh: true });
      const ml = rows && rows[0] && rows[0].map_layers;
      if (ml && (ml.cell_water || ml.cell_disease)) { setMapLayers(ml); setMapLayersState("ready"); }
      else setMapLayersState("empty");   // nothing stored yet — say so rather than draw a blank map
    } catch (_) { setMapLayersState("empty"); }
  }, [gridShadow, mapLayersState, year]);
  // What the map reads: the raw path's gridShadow when it exists, else the
  // lazily-fetched snapshot layers. Same shape either way, so MIYieldMap does
  // not need to know which one it got.
  const mapGridShadow = useMemo(
    () => gridShadow || (mapLayers ? { cell_water: mapLayers.cell_water || {}, cell_disease: mapLayers.cell_disease || {} } : null),
    [gridShadow, mapLayers]
  );
  const [inputGap, setInputGap] = useState(null);    // non-null = an input this model needs came back empty; say so, don't absorb it

  // ── USDA'S OWN ANSWER, BESIDE OURS ──────────────────────────────────────────
  // Added 2026-08-23 after an audit of the 2026 gap: Silas modelled 162.9 bu/ac
  // over 494,879 acres (80.61M bu) while USDA's August forecast was 124.0 over
  // 540,000 (66.96M). Nothing on this page said so, and auditing it needed a
  // hand-run against Quick Stats because the state-level series was not stored.
  //
  // THIS IS A BENCHMARK, NOT A CORRECTION. We do not move the model toward USDA
  // and we do not resolve the disagreement on the reader's behalf — this prints
  // both numbers and the difference. That is [[feedback_present_data_not_verdicts]]:
  // no score, no badge, no verdict stamp. Where the audit found evidence bearing
  // on WHICH is closer (NASS's own crop-condition series), the evidence is shown
  // with its source and the reader draws the conclusion.
  //
  // Acres are shown as prominently as yield on purpose. Harvested-grain acreage
  // is the hardest input in the whole model to pin down — the grain/silage split
  // is a harvest-time decision, and NASS revised its own 2025 number from 620k
  // (June) to 545k (Aug) to 450k (final). A production comparison that shows
  // only the yield halves invites reading an acreage disagreement as a yield one.
  const [usdaBench, setUsdaBench] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await sbFetch("nass_data", {
          select: "year,stat_type,value",
          filters: [
            "state=eq.NY", "crop=eq.CORN", "county=is.null",
            `year=gte.${year - 5}`, `year=lte.${year}`,
            "stat_type=in.(YIELD,YIELD_FORECAST,ACRES_HARVESTED,ACRES_HARVESTED_FORECAST,PRODUCTION,PRODUCTION_FORECAST)",
          ],
        });
        if (!alive) return;
        const get = (yr, ...types) => {
          for (const t of types) {
            const r = (rows || []).find((x) => Number(x.year) === yr && x.stat_type === t);
            if (r && r.value != null) return Number(r.value);
          }
          return null;
        };
        // The 5-yr normal is ACRE-WEIGHTED, not a mean of yields — a mean of
        // yields over years whose acreage swings 450k-600k is not a normal.
        let pSum = 0, aSum = 0, nYears = 0;
        for (let y = year - 5; y < year; y++) {
          const a = get(y, "ACRES_HARVESTED"), yl = get(y, "YIELD");
          if (a > 0 && yl > 0) { pSum += a * yl; aSum += a; nYears++; }
        }
        setUsdaBench({
          year,
          yield: get(year, "YIELD_FORECAST", "YIELD"),
          acres: get(year, "ACRES_HARVESTED_FORECAST", "ACRES_HARVESTED"),
          prod: get(year, "PRODUCTION_FORECAST", "PRODUCTION"),
          isForecast: get(year, "YIELD_FORECAST") != null,
          normYield: aSum > 0 ? pSum / aSum : null,
          normAcres: nYears ? aSum / nYears : null,
          normProd: nYears ? pSum / nYears : null,
          normN: nYears,
        });
      } catch (_) { if (alive) setUsdaBench(null); }
    })();
    return () => { alive = false; };
  }, [year]);

  // ── HOW WRONG THE ACRE HALF HAS BEEN ────────────────────────────────────────
  // The production headline is yield x acres and only the yield half has ever
  // carried a range on screen. Today all 39 counties read `acres_source:
  // "projected"` — NASS has published no county harvested figure for this crop
  // year — so the acre half is a 5-year mean with nothing correcting it.
  // `acreageBand` measures that rule against itself, rolling origin, on the
  // STATE series (the county series is too suppressed to support a band; see
  // src/acreageShadow.js for the measurement that settled it).
  //
  // Its own window, NOT usdaBench's: that fetch is year-5..year, which yields
  // one rolling origin. This needs the whole series.
  const [acreBand, setAcreBand] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await sbFetch("nass_data", {
          select: "year,value",
          filters: ["state=eq.NY", "crop=eq.CORN", "county=is.null", "stat_type=eq.ACRES_HARVESTED"],
        });
        if (!alive) return;
        const g = {};
        for (const r of rows || []) if (r.value != null) g[Number(r.year)] = Number(r.value);
        const b = acreageBand({ grainByYear: g, targetYear: year });
        setAcreBand(b && b.ok ? b : null);
      } catch (_) { if (alive) setAcreBand(null); }
    })();
    return () => { alive = false; };
  }, [year]);

  // Pure recompute from cached raw rows: builds the BASE case (default config)
  // and the SCENARIO (current Lab knobs). Instant — no Supabase round-trip —
  // so dragging a slider updates live.
  const recompute = useCallback(() => {
    const r = rawRef.current;
    if (!r) return;
    // WNY-avg daily precip (in) for the pace/PP workability projection. Source =
    // the high-def 338-cell grid (gridMET obs + Open-Meteo forecast) — the SAME
    // forecast the yield model and planting nowcast use, so the PP projection sees
    // the actual current dry stretch, not the older county POWER forecast. A good
    // run of weather now → workable days → the backlog clears → PP falls, with no
    // artificial cap. Falls back to county wxRows if the grid hasn't loaded.
    // Feeds BOTH base and scenario.
    const acc = {};
    const gridWx = (r.gridDaily && r.gridDaily.length) ? r.gridDaily : null;
    if (gridWx) {
      gridWx.forEach((w) => { if (w.precip_mm == null) return; const a = acc[w.obs_date] || (acc[w.obs_date] = { s: 0, n: 0 }); a.s += Number(w.precip_mm) / 25.4; a.n++; });
    } else {
      (r.wxRows || []).forEach((w) => { if (w.precip_in == null) return; const a = acc[w.obs_date] || (acc[w.obs_date] = { s: 0, n: 0 }); a.s += Number(w.precip_in); a.n++; });
    }
    const fcPrecip = {};
    Object.keys(acc).forEach((d) => { fcPrecip[d] = acc[d].s / acc[d].n; });
    // Latest NY topsoil %surplus (for saturated-soil workability cap).
    // Pull from soilRows: most recent week's TOPSOIL_PCT_SURPLUS for state=NY.
    let soilSurplusPct = null;
    if (r.soilRows && r.soilRows.length) {
      const surplus = r.soilRows.filter((x) => x.stat_type === "TOPSOIL_PCT_SURPLUS" && Number(x.year) === year);
      if (surplus.length) {
        const latestWk = Math.max(...surplus.map((x) => Number(x.week)));
        const v = surplus.find((x) => Number(x.week) === latestWk);
        if (v) soilSurplusPct = Number(v.value);
      }
    }
    const sp = miBuildSurveyPace(r.sFarmers, r.sIntents, r.sProg, year, null, fcPrecip, soilSurplusPct);
    const silData = { silAcRows: r.silAcRows, silYldRows: r.silYldRows, cowRows: r.cowRows };
    const signalsData = { soilRows: r.soilRows, usdmRows: r.usdmRows, condRows: r.condRows, curYrAcresRows: r.curYrAcresRows };
    // ── High-def gridMET weather (per-cell, corn×soil production-weighted) ──
    // Computed FIRST so it can drive the LIVE forecast (switched over 2026-05-30).
    // Counties without grid data fall back to the old county-centroid POWER factor.
    // Every input that reaches the model must be the SAME for an agent and for a
    // portal viewer, because this number gets quoted. When the survey tables were
    // admin-only the portal silently lost the pace factor AND — via the empty
    // cohorts below — the whole per-cell gridMET factor, and answered 157 where
    // the agent saw 168. Nothing threw; every gridShadow read is null-guarded.
    // So the missing input is now stated on the page instead of being absorbed:
    // a wrong number nobody can see is worse than a gap somebody can.
    const paceInputsMissing = !(r.sFarmers || []).length || !(r.sProg || []).length;
    setInputGap(paceInputsMissing ? "planting-survey pace" : null);
    const gridShadow = miGridShadowWeather((sp && sp.cohorts) || [], r.gridCells, r.gridDaily, r.gridClimo, MI_COEF, year);
    // Put the Weather Pulse + cumulative charts on the SAME grid feed as the model.
    if (gridShadow) { const gp = miGridWxPanels(r.gridDaily, r.gridClimo, year); if (gp) { gridShadow.wx_pulse = gp.wx_pulse; gridShadow.wx_chart = gp.wx_chart; } }
    setGridShadow(gridShadow);
    setBaseDataRaw(miComputeYield(year, ovr, r.yieldRows, r.paceRows, r.wxRows, sp, r.harvRows, r.plantedRows, null, silData, signalsData, gridShadow, r.rmaExpRows));
    // ── Mid-week corn nowcast + survey-history backtest ──
    // Reuses the same helpers PlantingSurveyScreen uses so the planting
    // story stays consistent across pages. Surfaces the latest survey,
    // today's modeled %, next Monday projection, and a real-data MAE.
    const { timeSeriesPoints, totalIntended } = buildCornSurveyTimeSeries(r.sFarmers, r.sIntents, r.sProg, year);
    if (timeSeriesPoints.length > 0 && totalIntended > 0) {
      // Kevin's simpler model: orange line = literal survey data, green
      // dotted + band = weather-driven projection from the latest survey
      // forward. No synthetic anchors, no chart manipulation. The
      // forward projection naturally uses the workability + willingness
      // curves and the observed + Open-Meteo forecast precip.
      setCornSurveyPoints(timeSeriesPoints);
      setCornNowcast(
        computeCornMidWeekNowcast({
          surveyTimeSeries: timeSeriesPoints,
          intendedAc: totalIntended,
          wxRows: r.wxRows || [],
          soilSurplusPct,
          asOfDate: new Date(),
          horizonDays: 45,    // project past today through end of meaningful planting season
        })
      );
      setCornBacktest(
        timeSeriesPoints.length >= 2
          ? backtestCornNowcastVsSurvey({
              surveyTimeSeries: timeSeriesPoints,
              intendedAc: totalIntended,
              wxRows: r.wxRows || [],
              soilSurplusPct,
            })
          : null
      );
      // Compute stale-reporting summary purely for the caption — does
      // NOT modify the chart's anchor. Tells the agent "by the way,
      // 15 farmers haven't reported in > 2 days" without rewriting
      // what's drawn.
      setCornStaleAdj(
        computeCornStaleAdjustedAnchor({
          sFarmers: r.sFarmers, sIntents: r.sIntents, sProg: r.sProg,
          wxRows: r.wxRows || [], soilSurplusPct,
          asOfDate: new Date(), freshDaysThreshold: 2,
        })
      );
    } else {
      setCornSurveyPoints([]);
      setCornNowcast(null);
      setCornBacktest(null);
      setCornStaleAdj(null);
    }
    // Scenario overrides: a null slider = use actual (= base for that input).
    const o = {};
    if (lab.heatPct != null) o.heatAnom = lab.heatPct / 100;
    if (lab.rainPct != null) o.rainAnom = lab.rainPct / 100;
    if (lab.acresPct != null) o.acresPct = lab.acresPct / 100;
    setDataRaw(miComputeYield(year, ovr, r.yieldRows, r.paceRows, r.wxRows, sp, r.harvRows, r.plantedRows, { ov: o }, silData, signalsData, gridShadow, r.rmaExpRows));   // signalsData already includes curYrAcresRows from above
  }, [year, ovr, lab]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const fipsList = MI_MODEL_COUNTIES.map((c) => c.fips).join(",");
      const [yieldRows, paceRows, wxRows, sFarmers, sIntents, sProg, harvRows, plantedRows, curYrAcresRows, silAcRows, silYldRows, cowRows, soilRows, usdmRows, condRows, gridCells, gridDaily, gridClimoA, gridClimoB, rmaExpRows] = await Promise.all([
        sbFetch("nass_data", { select: "county,year,value", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=eq.YIELD", `year=lt.${year}`], order: "county,year" }).catch(() => []),
        sbFetch("nass_data", { select: "year,week,value", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=eq.PCT_PLANTED"], order: "year,week,id.asc" }).catch(() => []),
        // 10y × 16 counties × 365 ≈ 58k rows — sbFetch caps at 50k and ascends, so
        // the cap dropped current-year data → weather pulse read zeros and the
        // per-county weather factor fell back to 1.0 (no anomaly to compute).
        // Tighten to 7y of history (still enough for a stable "normal") + bump
        // the row budget on this query so the most recent days are never cut.
        sbFetch("weather_county_daily", { select: "county_fips,obs_date,tmax_f,tmin_f,precip_in,is_forecast", filters: [`county_fips=in.(${fipsList})`, `obs_date=gte.${year - 7}-01-01`, `obs_date=lte.${year}-12-31`], order: "obs_date.desc" }).catch(() => []),
        // *_public views, NOT the base tables — and for the agent too, so both
        // sides of the app feed this model byte-identical inputs. The base tables
        // are admin-only, so a portal viewer used to get zero rows here, which
        // produced no cohorts, made miGridShadowWeather bail at its first line,
        // and dropped BOTH the planting-pace factor and the per-cell gridMET
        // weather factor. Howlett read 157 bu against the agent's 168 off the
        // same code — one model quietly running on two sets of inputs.
        // The views publish only these columns; survey_farmer_id stays an opaque
        // UUID because the farmers view carries no name, farmer_id or token.
        sbFetch("planting_survey_farmers_public", { select: "id,is_active" }).catch(() => []),
        sbFetch("planting_intentions_public", { select: "survey_farmer_id,crop,intended_acres" }).catch(() => []),
        sbFetch("planting_progress_public", { select: "survey_farmer_id,crop,acres_planted,submitted_at", order: "submitted_at.asc" }).catch(() => []),
        sbFetch("nass_data", { select: "county,year,value", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=eq.ACRES_HARVESTED", `year=lt.${year}`], order: "county,year" }).catch(() => []),
        // Historical TOTAL planted corn acres (grain + silage combined) — the
        // baseline for projecting this year's corn acres in the ground. Used by
        // miComputeYield: pred_grain_acres = planted5 × plantedByFpdFrac − silage_this_year.
        sbFetch("nass_data", { select: "county,year,value", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=eq.ACRES_PLANTED", `year=lt.${year}`], order: "county,year" }).catch(() => []),
        // Current-year NASS planted + harvested acres — lands progressively
        // through the year. State-level planted in the June Acreage Report;
        // county-level final in the Dec/Jan Crop Production. As soon as
        // NASS publishes for the current year, the model swaps from projected
        // acres (book pace × 5-yr avg) to the real number.
        sbFetch("nass_data", { select: "county,year,value,stat_type", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=in.(ACRES_PLANTED,ACRES_HARVESTED)", `year=eq.${year}`] }).catch(() => []),
        // State-level silage acres + silage yield + milk-cow inventory — feeds
        // the cow-driven silage-displacement model (back tons/cow from NY history).
        sbFetch("nass_data", { select: "year,value,county", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=eq.ACRES_HARVESTED_SILAGE"], order: "year,id.asc" }).catch(() => []),
        // Silage yield — STATE-level (county=null) for tons-per-cow calibration
        // AND county-level for per-county silage-yield baselines (silage yields
        // vary meaningfully by county: Allegany 5-yr avg ~16 t/ac, Cayuga ~20).
        sbFetch("nass_data", { select: "year,value,county", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=eq.YIELD_SILAGE"], order: "county,year,id.asc" }).catch(() => []),
        sbFetch("nass_data", { select: "year,value,county,stat_type", filters: ["state=eq.NY", "crop=eq.MILK", "stat_type=in.(MILK_COW_INVENTORY,MILK_COW_INVENTORY_COUNTY,MILK_HEIFERS_INVENTORY,MILK_HEIFERS_INVENTORY_COUNTY)"], order: "year,id.asc" }).catch(() => []),
        // NEW: NY topsoil moisture % short/adequate/surplus (weekly, growing season)
        sbFetch("nass_data", { select: "year,week,stat_type,value", filters: ["state=eq.NY", "crop=eq.MOISTURE"], order: "year,week,id.asc" }).catch(() => []),
        // NEW: USDM Drought Monitor per-WNY-county DSCI (manual sync)
        sbFetch("nass_data", { select: "fips,year,week,value", filters: ["crop=eq.DROUGHT", "stat_type=eq.USDM_DSCI"], order: "year,week,id.asc" }).catch(() => []),
        // NEW: NY crop condition %G/E (weekly during season, won't have data until late June)
        sbFetch("nass_data", { select: "year,week,value", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=eq.PCT_GOOD_EXCELLENT"], order: "year,week,id.asc" }).catch(() => []),
        // ── Phase 2 SHADOW: per-cell gridMET grid (current year) + 1991-2020
        //    per-cell climatology. Feeds miGridShadowWeather, a per-cell
        //    re-run of the cohort weather model shown beside the incumbent
        //    county-centroid factor. Does NOT affect the live forecast.
        //    grid daily only exists for the current season; a past-year
        //    selection returns nothing → shadow card hides. Climatology is
        //    ~337 cells × ~195 DOY > 50k (sbFetch's cap), so the early-stress
        //    (Apr 1) + silk/fill + season-to-date DOY window (91-285) is fetched
        //    in two halves, each well under 50k.
        sbFetch("wny_grid_cells", { select: "id,county_fips,corn_acres,nccpi_corn,awc,lat,lon", filters: [MI_GRID_STATE_FILTER] }).catch(() => []),
        // 338 cells × ~150 days crosses sbFetch's default 50k cap around late
        // May — an ascending fetch then silently drops the NEWEST rows, freezing
        // the whole page at ~05-28 (last-14d/next-14d/forecast strip all read 0
        // while season-to-date still works off the early rows). Start at Mar 1
        // (the model's earliest consumer is season_by_month at Apr 1; nothing
        // reads Jan–Feb) and raise the cap so a full season never truncates.
        // ── CAPS ARE A FUNCTION OF THE CELL COUNT, AND IT TRIPLED ────────────
        // At 338 cells: daily 103k under the 120k cap, climo halves 34k/39k under
        // the 50k default. At 1,271 they are 389k / 127k / 146k and all three
        // truncate. The daily fetch ASCENDS so its cap drops the NEWEST days (page
        // reads frozen); the climatology fetches order by cell_id so theirs drop
        // the HIGHEST ids — the eastern cells, inserted last, would vanish whole
        // while every western county still looked correct. Sized to ~1,600 cells.
        // ── AND THEN THE GRID LEFT NEW YORK, WHICH BREAKS THAT SIZING ────────
        // Pennsylvania added 1,231 cells on 2026-09-02, taking the table to 2,621
        // — past the 1,600 these caps were cut for. Raising them a THIRD time is
        // the wrong repair twice over: it moves ~800k rows a NEW YORK model never
        // reads, and it leaves the next state to re-break the same three numbers.
        // These read the ny_ VIEWS instead, which makes the caps a function of the
        // NY cell count — the thing this comment always meant. NY is 1,390 today,
        // so the ~25% cushion is real again and a second state cannot consume it.
        // KEEP IN STEP WITH api/_mi-snapshot.js — the cron and this page must feed
        // the model identical rows or the email quietly stops matching the screen.
        sbFetch("ny_grid_weather_daily", { select: "cell_id,obs_date,tmax_c,tmin_c,precip_mm,srad_wm2,vpd_kpa,is_forecast", filters: [`obs_date=gte.${year}-03-01`, `obs_date=lte.${year}-12-31`], order: "obs_date.asc", maxPages: 500 }).catch(() => []),
        sbFetch("ny_grid_climatology", { select: "cell_id,day_of_year,gdd_f,precip_mm,tmax_c,tmin_c,srad_wm2,vpd_kpa", filters: ["day_of_year=gte.91", "day_of_year=lte.190"], order: "cell_id.asc,day_of_year.asc", maxPages: 200 }).catch(() => []),
        sbFetch("ny_grid_climatology", { select: "cell_id,day_of_year,gdd_f,precip_mm,tmax_c,tmin_c,srad_wm2,vpd_kpa", filters: ["day_of_year=gte.191", "day_of_year=lte.305"], order: "cell_id.asc,day_of_year.asc", maxPages: 200 }).catch(() => []),
        // RMA's PUBLISHED expected county yield — the mechanistic forecast's anchor.
        // See MI_CORN and miComputeYield's effBaseline for why this beats the
        // trailing NASS mean. Public view (base table is admin-gated).
        sbFetch("rma_county_yields_public", { select: "crop_year,state,county,expected_yield,rma_final_yield", filters: ["crop=eq.CORN", "state=eq.NY"] }).catch(() => []),
      ]);
      rawRef.current = { yieldRows: yieldRows || [], paceRows: paceRows || [], wxRows: wxRows || [], sFarmers: sFarmers || [], sIntents: sIntents || [], sProg: sProg || [], harvRows: harvRows || [], plantedRows: plantedRows || [], curYrAcresRows: curYrAcresRows || [], silAcRows: silAcRows || [], silYldRows: silYldRows || [], cowRows: cowRows || [], soilRows: soilRows || [], usdmRows: usdmRows || [], condRows: condRows || [], gridCells: gridCells || [], gridDaily: gridDaily || [], gridClimo: [...(gridClimoA || []), ...(gridClimoB || [])], rmaExpRows: rmaExpRows || [] };
      recompute();
      setLoading(false);
    } catch (e) {
      setErr(e.message); setLoading(false);
    }
  }, [year, recompute]);

  // ── FAST PATH: read the model the cron already ran ─────────────────────────
  //
  // load() above fetches the 4km grid and rebuilds the model in the browser —
  // measured at 187,644 rows and 8.4s of pure transfer before anything renders,
  // every single visit. That is also what capped this page at 16 counties, since
  // sbFetch stops at 120k rows and NY+PA needs ~1.07M.
  //
  // api/_mi-snapshot.js runs the identical code (same module, same inputs, same
  // row caps) twice a day and stores what it computed. So the default path is now
  // to READ that: one row, ~27KB. The raw path stays for the two things that
  // genuinely need per-cell data — the Model Lab's live sliders and the map's
  // water/disease layers — and is loaded on demand instead of by everyone.
  const [rawLoaded, setRawLoaded] = useState(false);
  const [rawLoading, setRawLoading] = useState(false);
  const [snapshotAt, setSnapshotAt] = useState(null);
  const loadFast = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const rows = await sbFetch("mi_model_snapshot", { select: "result,computed_at", filters: [`crop_year=eq.${year}`], fresh: true });
      const stored = rows && rows[0] && rows[0].result;
      if (!stored || !stored.counties || !stored.counties.length) return false;   // fall through to the raw path
      setDataRaw(stored); setBaseDataRaw(stored);
      // JSON has no Date type, so anything that went through the database came
      // back as a string — and the PDF narrative calls lastObsDate.getMonth(),
      // which throws on one. Revive them before anything downstream touches it.
      setCornNowcast(miReviveDates(stored._nowcast) || null);
      setCornSurveyPoints(stored._surveyPoints || []);
      setCornBacktest(miReviveDates(stored._backtest) || null);
      setInputGap(stored._inputGap || null);
      setSnapshotAt(rows[0].computed_at || null);
      setLoading(false);
      return true;
    } catch (e) {
      return false;   // never block the page on the cache — fall back to computing
    }
  }, [year]);

  // Pull the raw rows and recompute locally. Needed for the Model Lab (its sliders
  // re-run the model per drag) and for a past year the cron never stored.
  // A portal session must never take this path — see the H-1 note on the component.
  const ensureRaw = useCallback(async () => {
    if (isPortal || rawLoaded || rawLoading) return;
    setRawLoading(true);
    try { await load(); setRawLoaded(true); } finally { setRawLoading(false); }
  }, [isPortal, rawLoaded, rawLoading, load]);

  useEffect(() => {
    let alive = true;
    setRawLoaded(false); setSnapshotAt(null);
    (async () => {
      const ok = await loadFast();
      if (!alive) return;
      if (ok) return;
      // No stored snapshot for this year. An agent recomputes from raw; a portal
      // session says so instead. Falling through here would fire the survey-view
      // reads, get 403s behind `.catch(() => [])`, and render a model that had
      // quietly lost its planting-pace and per-cell weather factors — which is
      // strictly worse than an honest empty state.
      if (isPortal) { setErr("The " + year + " yield model has not been computed yet today. It refreshes twice daily."); setLoading(false); return; }
      await load(); setRawLoaded(true);
    })();
    return () => { alive = false; };
  }, [year, isPortal]);
  // Opening the Lab, or touching a knob, needs the raw rows behind it.
  useEffect(() => { if (showLab) ensureRaw(); }, [showLab, ensureRaw]);
  useEffect(() => { if (rawLoaded && rawRef.current) recompute(); }, [lab, ovr]);

  // Persist the base-case headline so the daily email brief mirrors the page
  // (and inherits any future model refinement). Fires whenever the default
  // result recomputes; the upsert is idempotent on crop_year.
  useEffect(() => {
    if (!baseData || !baseData.counties) return;
    // Only write when the browser actually COMPUTED this. On the fast path
    // baseData came out of the very row we would be writing back, so writing
    // would be a no-op at best — and at worst it would stamp a stale
    // computed_at over the cron's fresher one and make the brief look current
    // when it isn't.
    if (!rawRef.current) return;
    // ── ⚠ AND THE RESULT MUST BE FOR THE YEAR BEING WRITTEN ────────────────
    // `baseDataRaw` is whatever the model last produced, and `year` is what the
    // picker is on. They separate whenever the year changes while a computation
    // is still in flight, or when a stored snapshot for one year is re-rendered
    // under another — and the writes below key on `year`, so the older result
    // would be stamped into THIS year's row. `mi_model_snapshot.payload` is read
    // as all of New York by the email brief and by the Coverage Advisor, so a
    // cross-year write reaches two surfaces that cannot tell it happened.
    //
    // It refuses BEFORE either payload is built, not merely before the upserts:
    // a guard placed after the work still writes the day somebody moves a call
    // above it. The calendar guard further down is NOT a substitute — that one
    // stops a PAST year being written as today's row at all, which is a
    // different question from the result and the selection disagreeing.
    if (Number(baseDataRaw && baseDataRaw.year) !== Number(year)) return;
    // ── THE SNAPSHOT PAYLOAD IS STATEWIDE, WHATEVER REGION IS ON SCREEN ─────
    // `payload` below is built from `baseData`, which is REGION-FILTERED, and the
    // two readers of mi_model_snapshot.payload both take it as all of New York:
    // api/_mi-brief.js renders it under a New York header, and the Coverage
    // Advisor pulls payload.mi_counties for per-county yields. Writing the
    // filtered one from a page left on Western NY put 16 counties in a column 39
    // are read out of — the same defect the mi_yield_daily block below already
    // names, one line above its own fix. baseDataRaw is the unfiltered result.
    const payloadNy = miSnapshotFromResult(baseDataRaw, cornNowcast, year);
    const payload = miSnapshotFromResult(baseData, cornNowcast, year);
    if (payloadNy) sbUpsert("mi_model_snapshot", [{ crop_year: year, payload: payloadNy, computed_at: new Date().toISOString() }], "crop_year").catch(() => {});
    // Daily history — one row per (crop_year, day). Idempotent per day (latest
    // model run that day wins), so the prediction can be charted over the season.
    // The day is EASTERN, via the same helper api/_mi-snapshot.js uses: this
    // table now has two writers, and on a UTC day-key a page opened after 8pm ET
    // would stamp TOMORROW and sit alongside the cron's row for the same day.
    if (payload && year === nowYear) {
      const today = miSnapshotDay();
      // THE CALL FREEZES ON OCT 10 (src/miFinalCall.js) — the same rule the cron
      // obeys. A page opened after the freeze must not write a moving number into
      // the series the final call is scored from.
      if (!miMayWriteDaily(year, today)) return;
      // ── WRITE EVERY REGION, NOT THE ONE THAT HAPPENS TO BE SELECTED ──────
      // This used to write `payload`, which is built from the REGION-FILTERED
      // result — so opening the page with Western NY active stamped a 16-county
      // number into a row that named no region, on top of whatever the cron had
      // written for that day. The series was then a mixture of two different
      // county sets depending on who wrote last, and nothing recorded which.
      // Both rows are written from baseDataRaw (unfiltered) through the SAME
      // shared filter the cron uses, so page and cron cannot disagree.
      const rows = [];
      for (const key of Object.keys(MI_REGIONS)) {
        const p = key === region ? payload : miSnapshotFromResult(miFilterResult(baseDataRaw, key), cornNowcast, year);
        if (!p) continue;
        rows.push({
          crop_year: year, snapshot_date: today, region: key,
          predicted_yield: p.predicted_yield, five_yr_avg_yield: p.five_yr_avg_yield,
          production_bu: p.production_bu, production_pct: p.production_pct,
          info_captured_pct: p.info_captured_pct, phase: p.phase,
          model_version: MI_MODEL_VERSION, computed_at: new Date().toISOString(),
        });
      }
      if (rows.length) {
        sbUpsert("mi_yield_daily", rows, "crop_year,snapshot_date,region")
          .then(() => setYieldHistory((prev) => {
            const mine = rows.filter((r) => r.region === region);
            const o = (prev || []).filter((r) => r.snapshot_date !== today);
            return [...o, ...mine].sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date));
          }))
          .catch(() => {});
      }
    }
  }, [baseData, baseDataRaw, region, cornNowcast, year, nowYear]);

  // One-click weather sync (Kevin's call: manual button instead of a cron).
  // Backfill loops allFips from index 0 under a 45s budget, so re-running the
  // bulk call never progresses past the cutoff — drive it per-county instead.
  // Uses the logged-in Supabase session (getApiAuthHeaders) — no secrets.
  // Only works on the deployed site (/api/* doesn't exist on localhost).
  // ── Manual refresh: USDM drought + NASS soil moisture + NASS crop condition
  // No cron for these (Kevin's call) — this button is the only path to fresh
  // data. Reloads in place after the sync completes so the new values appear
  // without the user having to remember to hard-refresh the browser.
  const refreshSignals = useCallback(async () => {
    setSignalsRefreshing(true);
    setSignalsRefreshMsg("Pulling USDM drought monitor...");
    try {
      const headers = getApiAuthHeaders();
      const usdmRes = await fetch("/api/nass-sync?datasets=usdm", { method: "POST", headers });
      const usdmJson = await usdmRes.json().catch(() => ({}));
      if (!usdmRes.ok) throw new Error(`USDM failed: ${usdmJson.error || usdmRes.status}`);
      const usdmCount = usdmJson.results?.usdm?.upserted || 0;
      setSignalsRefreshMsg(`USDM ✅ ${usdmCount} rows. Pulling soil moisture + crop condition...`);
      const nassRes = await fetch("/api/nass-sync?datasets=soilmoisture,condition", { method: "POST", headers });
      const nassJson = await nassRes.json().catch(() => ({}));
      if (!nassRes.ok) throw new Error(`NASS failed: ${nassJson.error || nassRes.status}`);
      const soilCount = nassJson.results?.soilmoisture?.upserted || 0;
      const condCount = nassJson.results?.condition?.upserted || 0;
      setSignalsRefreshMsg(`✅ USDM ${usdmCount} · Soil ${soilCount} · Condition ${condCount} rows. Reloading data...`);
      await load();
      setSignalsRefreshMsg(`✅ Refreshed at ${new Date().toLocaleTimeString()} — USDM ${usdmCount}, Soil ${soilCount}, Condition ${condCount} rows.`);
    } catch (e) {
      setSignalsRefreshMsg(`❌ ${e.message}`);
    }
    setSignalsRefreshing(false);
  }, [load]);

  // ── Export polished PDF report ──────────────────────────────────────────
  // Opens a new window with a print-styled HTML report sized for Letter/A4:
  // header w/ Clover Ag logo, headline KPIs, the LLM situation summary,
  // weather pulse cards, cumulative GDD + precip charts (inline SVG), an
  // SVG county map colored by predicted yield, and the per-county yield
  // table sorted predicted yield desc. User chooses "Save as PDF" from the
  // browser print dialog. Matches the existing planting-progress print
  // pattern — no new bundled dependencies. d3 + TopoJSON are loaded once
  // in the parent window for the on-screen map; we reuse those here to
  // render the print map as static inline SVG (no script load in the
  // print window itself).
  const [exporting, setExporting] = useState(false);
  const [wxOpen, setWxOpen] = useState(false);   // cumulative GDD/rain charts collapsed by default
  const [wxBig, setWxBig] = useState(false);     // enlarge the GDD/rain charts (stack full-width)
  // Elevator draw-area catchment — a Set of county names, shared by the map
  // (click-to-toggle + highlight) and the Draw Area card. Empty = all WNY.
  const [catchment, setCatchment] = useState(() => new Set());
  // Daily prediction history (this crop year) — charts how the model's call has
  // evolved over the season. Accrues from the day this shipped; no backfill.
  // ── THE SERIES IS PER REGION, because the county set is not a constant ──────
  // Before this was keyed by region the chart drew one line whose MEANING changed
  // on 2026-08-13, when the model went 16 -> 39 counties: production stepped
  // 48-56M to 78-80M bushels and the header reported the jump as a season
  // decline. Refetch on region so the line always means one thing end to end.
  const [yieldHistory, setYieldHistory] = useState([]);
  useEffect(() => {
    sbFetch("mi_yield_daily", { select: "snapshot_date,predicted_yield,five_yr_avg_yield,production_bu,production_pct,info_captured_pct,region,model_version", filters: [`crop_year=eq.${year}`, `region=eq.${region}`], order: "snapshot_date.asc" })
      .then((rows) => setYieldHistory(rows || [])).catch(() => setYieldHistory([]));
  }, [year, region]);
  // ── THE FINAL CALL (frozen Oct 10) and what NASS finally said ──────────────
  // mi_yield_final_call is sql/106; before it is applied the read fails and the
  // page falls back to the mi_yield_daily freeze row (identical regional figures).
  // The NASS read is for SCORING only: stat_type YIELD is the final (in-season
  // forecasts are stored as YIELD_FORECAST and are never read here).
  const [finalCallRows, setFinalCallRows] = useState([]);
  const [finalNass, setFinalNass] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!miIsFrozen(year, miSnapshotDay())) { setFinalCallRows([]); setFinalNass([]); return () => { alive = false; }; }
    // No explicit select: the table is sql/106 and is not in tools/schema.json until
    // it is applied and the snapshot regenerated — then name the columns here.
    sbFetch("mi_yield_final_call", { filters: [`crop_year=eq.${year}`] })
      .then((r) => { if (alive) setFinalCallRows(r || []); }).catch(() => { if (alive) setFinalCallRows([]); });
    sbFetch("nass_data", { select: "county,fips,crop,year,stat_type,value", filters: ["state=eq.NY", "crop=eq.CORN", "stat_type=in.(YIELD,ACRES_HARVESTED)", `year=eq.${year}`] })
      .then((r) => { if (alive) setFinalNass(r || []); }).catch(() => { if (alive) setFinalNass([]); });
    return () => { alive = false; };
  }, [year]);
  const finalCall = useMemo(
    () => miPickFinalCall({ cropYear: year, region, day: miSnapshotDay(), finalRows: finalCallRows, dailyRows: yieldHistory }),
    [year, region, finalCallRows, yieldHistory]
  );
  const lblMd = (d) => { const p = String(d || "").split("-"); return p.length === 3 ? `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+p[1] - 1]} ${+p[2]}` : String(d || ""); };
  const finalScore = useMemo(
    () => (finalCall ? miScoreFinalCall({
      cropYear: Number(year), finalCalls: [finalCall, ...finalCallRows.filter((r) => r && r.region !== finalCall.region)],
      nassRows: finalNass, wnyFips: MI_WNY_FIPS, modelFips: MI_MODEL_COUNTIES.map((c) => c.fips),
    }) : null),
    [finalCall, finalCallRows, finalNass, year]
  );
  // Forecast freshness — latest synced_at of any Open-Meteo forecast row. Drives
  // the "forecast updated Xh ago" pill so a stale feed is visible at a glance.
  const [fcSyncedAt, setFcSyncedAt] = useState(null);
  React.useEffect(() => {
    let alive = true;
    sbFetch("wny_grid_weather_daily", { select: "synced_at", filters: ["is_forecast=eq.true", "limit=1"], order: "synced_at.desc" })
      .then((r) => { if (alive && r && r[0]) setFcSyncedAt(r[0].synced_at); })
      .catch(() => {});
    return () => { alive = false; };
  }, [data]);
  const buildAndPrintMi = useCallback(async () => {
    if (!data || exporting) return;
    setExporting(true);
    const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
    const CLOVER_URL = origin + "/clover-mark.png";
    const WORDMARK_URL = origin + "/silas-wordmark-dark.svg";
    const printDate = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const generatedAt = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const fmt0 = (n) => (n == null || isNaN(n) ? "—" : Math.round(Number(n)).toLocaleString());
    const fmt1 = (n) => (n == null || isNaN(n) ? "—" : Number(n).toFixed(1));
    const fmtPct = (n, digits = 1) => (n == null || isNaN(n) ? "—" : `${Number(n) >= 0 ? "+" : ""}${(Number(n) * 100).toFixed(digits)}%`);
    const fmtDelta = (n) => (n == null || isNaN(n) ? "—" : `${Number(n) >= 0 ? "+" : ""}${Number(n).toFixed(1)}`);

    // Compact line-chart SVG builder (cur + 1995–2025 normal). Matches the
    // on-screen chart visually but is fully self-contained inline SVG so
    // the print window doesn't need d3.
    function lineChartSvg({ title, sub, xLabels, series, w = 720, h = 280, fcFrom = null, valFmt = null }) {
      const padL = 50, padR = 16, padT = 58, padB = 36;
      const innerW = w - padL - padR;
      const innerH = h - padT - padB;
      const allVals = series.flatMap((s) => (s.vals || []).filter((v) => v != null && !isNaN(v)));
      const maxV = allVals.length ? Math.max(...allVals) : 1;
      // Nice round max so y-axis labels are clean
      const niceMax = (() => {
        if (maxV <= 1) return Math.ceil(maxV * 10) / 10;
        if (maxV <= 10) return Math.ceil(maxV);
        const pow = Math.pow(10, Math.floor(Math.log10(maxV)));
        return Math.ceil(maxV / (pow / 2)) * (pow / 2);
      })();
      const n = xLabels.length;
      const xAt = (i) => padL + (n <= 1 ? innerW / 2 : (i * innerW) / (n - 1));
      const yAt = (v) => padT + innerH - (v / (niceMax || 1)) * innerH;
      const yTicks = 4;
      let grid = "";
      for (let i = 0; i <= yTicks; i++) {
        const v = (niceMax * i) / yTicks;
        const yy = yAt(v);
        grid += `<line x1="${padL}" x2="${padL + innerW}" y1="${yy.toFixed(1)}" y2="${yy.toFixed(1)}" stroke="#e6ebe6" stroke-width="1"/>`;
        const label = niceMax < 10 ? v.toFixed(1) : Math.round(v).toLocaleString();
        grid += `<text x="${padL - 6}" y="${(yy + 3.5).toFixed(1)}" font-size="9.5" fill="#60735f" text-anchor="end" font-family="'Plus Jakarta Sans',sans-serif">${label}</text>`;
      }
      // x labels (every Nth so they don't crowd)
      let xAxis = "";
      const step = Math.max(1, Math.floor(n / 8));
      for (let i = 0; i < n; i += step) {
        xAxis += `<text x="${xAt(i).toFixed(1)}" y="${(padT + innerH + 16).toFixed(1)}" font-size="9.5" fill="#60735f" text-anchor="middle" font-family="'Plus Jakarta Sans',sans-serif">${esc(xLabels[i])}</text>`;
      }
      // Forecast boundary for series[0] (the current-year line)
      const fb = fcFrom != null && fcFrom > 0 && fcFrom < n ? fcFrom : null;
      const vfmt = valFmt || ((v) => (niceMax < 10 ? v.toFixed(1) : Math.round(v).toLocaleString()));
      // Soft area fill under series[0] (observed portion only)
      let areaSvg = "";
      if (series[0] && series[0].vals) {
        const pts = [];
        const hiA = fb != null ? fb - 1 : (series[0].vals.length - 1);
        for (let i = 0; i <= hiA; i++) { const v = series[0].vals[i]; if (v == null || isNaN(v)) continue; pts.push(`${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`); }
        if (pts.length >= 2) {
          const x0 = xAt(0).toFixed(1), xN = xAt(hiA).toFixed(1), yB = (padT + innerH).toFixed(1);
          areaSvg = `<polygon points="${x0},${yB} ${pts.join(" ")} ${xN},${yB}" fill="${series[0].color}" fill-opacity="0.08"/>`;
        }
      }
      // Today marker (last observed point)
      let todaySvg = "";
      if (fb != null) {
        const tx = xAt(fb - 1).toFixed(1);
        todaySvg = `<line x1="${tx}" x2="${tx}" y1="${padT}" y2="${(padT + innerH).toFixed(1)}" stroke="#c9a84a" stroke-width="1" stroke-dasharray="3,3"/>`
          + `<text x="${tx}" y="${(padT - 1).toFixed(1)}" font-size="8.5" fill="#a9842a" text-anchor="middle" font-family="'Plus Jakarta Sans',sans-serif">today</text>`;
      }
      // Lines (skip null-bearing segments by chunking polyline); series[0] splits
      // into a solid observed run + a translucent dashed forecast tail.
      let linesSvg = "", endLabels = "";
      const endPts = [];
      series.forEach((s, si) => {
        const vals = s.vals || [];
        const split = si === 0 && fb != null;
        const drawRun = (lo, hi, dash, opacity) => {
          const segs = [[]];
          for (let i = lo; i <= hi; i++) {
            const v = vals[i];
            if (v == null || isNaN(v)) { if (segs[segs.length - 1].length) segs.push([]); continue; }
            segs[segs.length - 1].push(`${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`);
          }
          const dashAttr = dash ? ` stroke-dasharray="${dash}"` : "";
          for (const seg of segs) { if (seg.length < 2) continue; linesSvg += `<polyline fill="none" stroke="${s.color}" stroke-width="${s.w || 2}" stroke-linecap="round" stroke-linejoin="round" opacity="${opacity}" points="${seg.join(" ")}"${dashAttr}/>`; }
        };
        if (split) { drawRun(0, fb - 1, s.dash || null, 1); drawRun(fb - 1, vals.length - 1, "5,4", 0.55); }
        else drawRun(0, vals.length - 1, s.dash || null, 1);
        // Record the endpoint; labels are placed after the loop so converging
        // series (e.g. GDD 2026 ≈ normal) don't stack their labels on top of
        // each other. The dot sits at the true endpoint regardless.
        let li = vals.length - 1; while (li >= 0 && (vals[li] == null || isNaN(vals[li]))) li--;
        if (li >= 0) {
          const ex = xAt(li), ey = yAt(vals[li]);
          endLabels += `<circle cx="${ex.toFixed(1)}" cy="${ey.toFixed(1)}" r="2.6" fill="${s.color}"/>`;
          endPts.push({ ex, ey, color: s.color, text: vfmt(vals[li]) });
        }
      });
      // De-conflict end-of-line value labels: keep the topmost at its natural
      // spot and push any label within LABEL_H of the one above it downward,
      // clamped to the plot area, so overlapping series stay legible.
      const LABEL_H = 12;
      const placed = endPts
        .map((p) => ({ ...p, ly: p.ey - 5 }))
        .sort((a, b) => a.ly - b.ly);
      for (let i = 1; i < placed.length; i++) {
        if (placed[i].ly - placed[i - 1].ly < LABEL_H) placed[i].ly = placed[i - 1].ly + LABEL_H;
      }
      const maxLy = padT + innerH - 2;
      for (const p of placed) {
        const ly = Math.min(p.ly, maxLy);
        endLabels += `<text x="${(p.ex - 4).toFixed(1)}" y="${ly.toFixed(1)}" font-size="9.5" font-weight="700" fill="${p.color}" text-anchor="end" font-family="'Plus Jakarta Sans',sans-serif">${p.text}</text>`;
      }
      // Legend — its own row BELOW the title + subtitle (was overlapping the subtitle).
      let legendSvg = "";
      let lx = padL;
      for (const s of series) {
        legendSvg += `<line x1="${lx}" x2="${lx + 18}" y1="49" y2="49" stroke="${s.color}" stroke-width="${s.w || 2}"${s.dash ? ` stroke-dasharray="${s.dash}"` : ""}/>`;
        legendSvg += `<text x="${lx + 24}" y="52.5" font-size="10" fill="#1a2e1a" font-family="'Plus Jakarta Sans',sans-serif">${esc(s.name)}</text>`;
        lx += s.name.length * 5.5 + 56;
      }
      return `<svg viewBox="0 0 ${w} ${h}" width="100%" preserveAspectRatio="xMidYMid meet" style="display:block;max-width:100%;background:#fafdfa;border-radius:8px;border:1px solid #e6ece6;">
        <text x="${padL}" y="20" font-size="13" font-weight="700" fill="#1a2e1a" font-family="'Plus Jakarta Sans',sans-serif">${esc(title)}</text>
        <text x="${padL}" y="35" font-size="10" fill="#6b7c6b" font-family="'Plus Jakarta Sans',sans-serif">${esc(sub || "")}</text>
        ${legendSvg}
        ${grid}
        ${areaSvg}
        <line x1="${padL}" x2="${padL + innerW}" y1="${(padT + innerH).toFixed(1)}" y2="${(padT + innerH).toFixed(1)}" stroke="#60735f" stroke-width="1"/>
        <line x1="${padL}" x2="${padL}" y1="${padT.toFixed(1)}" y2="${(padT + innerH).toFixed(1)}" stroke="#60735f" stroke-width="1"/>
        ${todaySvg}
        ${linesSvg}
        ${endLabels}
        ${xAxis}
      </svg>`;
    }

    // Static inline-SVG county yield map. Reuses parent-window d3 (already
    // loaded via d3Promise) and fetches the same TopoJSON the on-screen
    // MIYieldMap uses. Color scale is a 6-stop green ramp keyed to the
    // actual yield range so the visual encoding is anchored to this
    // particular run (not a fixed [120,200] domain that would flatten
    // small spreads). Each county is filled + outlined and labeled with
    // its name on top and predicted yield bu/ac underneath.
    async function buildMapSvg(_counties) {
      try {
        const d3 = (typeof window !== "undefined" && window.d3) ? window.d3 : await d3Promise;
        if (!d3) return null;
        const topo = await fetch("https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json").then((r) => r.json()).catch(() => null);
        if (!topo) return null;
        const wnyFips = new Set(MI_MODEL_COUNTIES.map((c) => c.fips));
        const features = decodeTopo(topo, topo.objects.counties).filter((f) => wnyFips.has(f.properties.fips));
        if (!features.length) return null;
        const geoData = { type: "FeatureCollection", features };
        const W = 760, H = 440;
        const proj = d3.geoMercator().fitSize([W - 20, H - 70], geoData);
        const pathGen = d3.geoPath().projection(proj);

        const palette = ["#e8f0e8", "#c8dec8", "#a0c8a0", "#7ab07a", "#4a8b4a", "#2d6a2d"];
        const yields = _counties.map((c) => c.forecast_yield).filter((v) => v != null);
        const lo = yields.length ? Math.min(...yields) : 0;
        const hi = yields.length ? Math.max(...yields) : 1;
        const span = Math.max(0.1, hi - lo);
        const colorFor = (y) => {
          if (y == null || isNaN(y)) return "#f2f2f0";
          const t = Math.max(0, Math.min(0.9999, (y - lo) / span));
          return palette[Math.floor(t * palette.length)];
        };
        const byName = {};
        for (const c of _counties) byName[String(c.name).toUpperCase()] = c;

        let paths = "";
        let labels = "";
        for (const feat of features) {
          const c = byName[String(feat.properties.name).toUpperCase()];
          const yld = c ? c.forecast_yield : null;
          const d = pathGen(feat);
          if (!d) continue;
          paths += `<path d="${d}" fill="${colorFor(yld)}" stroke="#fff" stroke-width="1.2"/>`;
          const cen = pathGen.centroid(feat);
          if (cen && !isNaN(cen[0])) {
            const nm = feat.properties.name;
            const ystr = yld != null ? `${Number(yld).toFixed(1)}` : "—";
            // Choose label color based on fill darkness (last 2 palette steps are dark)
            const darkBg = yld != null && (yld - lo) / span >= 0.66;
            const txt = darkBg ? "#fff" : "#1a2e1a";
            const shadow = darkBg ? "" : ` style="paint-order:stroke;stroke:#ffffffcc;stroke-width:2.4;stroke-linejoin:round;"`;
            labels += `<text x="${cen[0].toFixed(1)}" y="${(cen[1] - 4).toFixed(1)}" font-size="9.5" font-weight="700" text-anchor="middle" fill="${txt}" font-family="'Plus Jakarta Sans',sans-serif"${shadow}>${nm}</text>`;
            labels += `<text x="${cen[0].toFixed(1)}" y="${(cen[1] + 8).toFixed(1)}" font-size="11" font-weight="800" text-anchor="middle" fill="${txt}" font-family="'Plus Jakarta Sans',sans-serif"${shadow}>${ystr}</text>`;
          }
        }
        // Color-scale legend (bottom band)
        const legendW = 360, legendH = 14;
        const legendX = (W - legendW) / 2, legendY = H - 36;
        let legend = "";
        for (let i = 0; i < palette.length; i++) {
          legend += `<rect x="${(legendX + (i * legendW) / palette.length).toFixed(1)}" y="${legendY}" width="${(legendW / palette.length).toFixed(2)}" height="${legendH}" fill="${palette[i]}"/>`;
        }
        legend += `<rect x="${legendX}" y="${legendY}" width="${legendW}" height="${legendH}" fill="none" stroke="#60735f" stroke-width="0.6"/>`;
        legend += `<text x="${legendX}" y="${(legendY - 6).toFixed(1)}" font-size="10" font-weight="700" fill="#1a2e1a" font-family="'Plus Jakarta Sans',sans-serif">${lo.toFixed(0)} bu/ac</text>`;
        legend += `<text x="${legendX + legendW}" y="${(legendY - 6).toFixed(1)}" font-size="10" font-weight="700" fill="#1a2e1a" text-anchor="end" font-family="'Plus Jakarta Sans',sans-serif">${hi.toFixed(0)} bu/ac</text>`;
        legend += `<text x="${legendX + legendW / 2}" y="${(legendY + legendH + 12).toFixed(1)}" font-size="9.5" fill="#5a7a5a" text-anchor="middle" font-family="'Plus Jakarta Sans',sans-serif">Lower yield ← color scale → Higher yield</text>`;

        return `<svg viewBox="0 0 ${W} ${H}" width="100%" preserveAspectRatio="xMidYMid meet" style="display:block;max-width:100%;background:#fafdfa;border-radius:8px;border:1px solid #e6ece6;">
          <text x="10" y="18" font-size="13" font-weight="700" fill="#1a2e1a" font-family="'Plus Jakarta Sans',sans-serif">Predicted corn yield by county · ${year}</text>
          <text x="10" y="32" font-size="10" fill="#6b7c6b" font-family="'Plus Jakarta Sans',sans-serif">New York · forecast bu/ac</text>
          ${paths}
          ${labels}
          ${legend}
        </svg>`;
      } catch (e) {
        return null;
      }
    }

    // ── Headline KPIs ────────────────────────────────────────────────────
    // ACRE-WEIGHTED regional yield (= total bu ÷ total acres) — must be IDENTICAL to
    // the in-app hero KPI (`wny.forecast`), the brief, and the production number, or
    // the PDF contradicts the screen. (This was a simple unweighted mean and read ~3 bu
    // LOW — the big-acre counties like Monroe/Orleans run above-average yield, and the
    // old comment here wrongly claimed the hero was unweighted too. Fixed 2026-06-23.)
    const counties = (data.counties || []).filter((c) => !c.error && c.forecast_yield != null);
    const wavgPdf = (k) => {
      let wn = 0, wd = 0, sn = 0, ss = 0;
      for (const c of counties) { if (c[k] == null) continue; ss += c[k]; sn++; const w = c.pred_acres > 0 ? c.pred_acres : 0; wn += c[k] * w; wd += w; }
      return wd > 0 ? wn / wd : (sn > 0 ? ss / sn : null);
    };
    const wnyYield = miR1(wavgPdf("forecast_yield"));
    const wny5yrMean = miR1(wavgPdf("recent_5yr_mean"));
    const wnyVsMean = wnyYield != null && wny5yrMean != null ? miR1(wnyYield - wny5yrMean) : null;
    const wnyCountyCount = counties.length;
    // Acre-weighted book weather factor (matches the in-app hero KPI).
    const wnyWxFactor = (() => { const v = wavgPdf("total_wx_factor"); return v != null ? Math.round(v * 1000) / 1000 : null; })();
    // Season progress bar (Apr 20 → Oct 10) — phase-colored, today marker, future dimmed.
    const seasonBarHtml = (() => {
      const sStart = new Date(year, 3, 20), sEnd = (() => { const [fy, fm, fd] = miFreezeDate(year).split("-").map(Number); return new Date(fy, fm - 1, fd); })(), now = new Date();
      const span = sEnd - sStart;
      const pct = Math.max(0, Math.min(100, ((now - sStart) / span) * 100));
      const todayLabel = now.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const xOf = (d) => Math.max(0, Math.min(100, ((d - sStart) / span) * 100));
      const phaseDefs = [
        { to: new Date(year, 5, 15), label: "Plant", c: "#6b7a4a" },
        { to: new Date(year, 6, 20), label: "Grow", c: "#4a9e4a" },
        { to: new Date(year, 7, 15), label: "Silk", c: "#e0b341" },
        { to: new Date(year, 8, 25), label: "Fill", c: "#3f8f5a" },
        { to: sEnd, label: "Mature", c: "#c98a3a" },
      ];
      let prev = 0; const segs = [], labs = [];
      for (const ph of phaseDefs) {
        const end = xOf(ph.to), w = end - prev, reached = ((prev + end) / 2) <= pct;
        segs.push(`<div style="position:absolute;left:${prev}%;width:${w}%;top:0;height:100%;background:${ph.c};"></div>`);
        labs.push(`<div style="position:absolute;left:${(prev + end) / 2}%;transform:translateX(-50%);white-space:nowrap;font-size:9px;color:${reached ? "#3a5a3a" : "#677167"};font-weight:${reached ? 700 : 400};">${ph.label}</div>`);
        prev = end;
      }
      return `<div style="margin:0 0 14px;">
        <div style="display:flex;justify-content:space-between;font-size:10px;color:#5a7a5a;margin-bottom:5px;"><span>Apr 20</span><span style="color:#1a2e1a;font-weight:700;">${todayLabel}</span><span>Oct 10</span></div>
        <div style="position:relative;height:11px;">
          <div style="position:absolute;inset:0;border-radius:5px;overflow:hidden;background:#e8eee8;">${segs.join("")}${pct < 100 ? `<div style="position:absolute;left:${pct}%;width:${100 - pct}%;top:0;height:100%;background:rgba(255,255,255,0.55);"></div>` : ""}</div>
          <div style="position:absolute;left:${pct}%;top:-3px;width:3px;height:17px;background:#1a2e1a;border-radius:2px;transform:translateX(-50%);"></div>
        </div>
        <div style="position:relative;height:13px;margin-top:3px;">${labs.join("")}</div>
      </div>`;
    })();

    const prodPredBu = data.production?.pred_bu;
    const prodNormBu = data.production?.normal_bu;
    const prodAnomPct = data.production?.pct;
    const infoPct = data.uncertainty?.confidence_pct;
    const phase = data.uncertainty?.phase || "—";
    const nextSharpening = data.uncertainty?.next_sharpening || "";

    const kpiCard = (label, value, sub, color) => `
      <div class="kpi">
        <div class="kpi-label">${esc(label)}</div>
        <div class="kpi-value" style="color:${color || "#1a2e1a"};">${value}</div>
        <div class="kpi-sub">${sub || ""}</div>
      </div>`;

    const yieldSubLines = [
      `Across ${wnyCountyCount} WNY counties`,
      wnyVsMean != null ? `${fmtDelta(wnyVsMean)} vs 5-yr mean` : null,
    ].filter(Boolean).join(" · ");
    const kpis = `
      <div class="kpi-strip">
        ${kpiCard("Predicted yield", `${fmt1(wnyYield)}<span class="unit"> bu/ac</span>`, yieldSubLines, "#1a4a1a")}
        ${kpiCard("Predicted production", `${prodPredBu != null ? (prodPredBu / 1e6).toFixed(2) : "—"}<span class="unit">M bu</span>`, prodAnomPct != null ? `${fmtPct(prodAnomPct)} vs normal (${prodNormBu != null ? (prodNormBu / 1e6).toFixed(2) : "—"}M)` : "", prodAnomPct != null && prodAnomPct < -0.05 ? "#c0392b" : prodAnomPct != null && prodAnomPct > 0.05 ? "#2d6a2d" : "#1a2e1a")}
        ${kpiCard("Total weather factor", wnyWxFactor != null ? `×${wnyWxFactor.toFixed(3)}` : "—", "early stress × soil-water × silking", wnyWxFactor == null ? "#1a2e1a" : wnyWxFactor >= 1.01 ? "#2d6a2d" : wnyWxFactor <= 0.97 ? "#c0392b" : "#b88500")}
        ${kpiCard("Info captured", `${infoPct != null ? infoPct : "—"}<span class="unit">%</span>`, "of season information observed", infoPct >= 70 ? "#2d6a2d" : infoPct >= 40 ? "#b88500" : "#7a5b00")}
        ${kpiCard("Season phase", esc(phase), esc(nextSharpening), "#1a4a7a")}
      </div>`;

    // ── Situation summary ────────────────────────────────────────────────
    const summaryHtml = summary
      ? summary.split("\n\n").map((p) => `<p>${esc(p)}</p>`).join("")
      : `<p class="muted-italic">No prediction explanation generated yet. Click "Generate summary" on the dashboard before exporting to include it.</p>`;

    // ── Weather pulse cards ──────────────────────────────────────────────
    const wp = data.wx_pulse || {};
    const pulseAnom = (anom) => (anom == null ? "" : `<span class="anom ${anom > 0.05 ? "pos" : anom < -0.05 ? "neg" : "neu"}">${anom >= 0 ? "▲" : "▼"} ${Math.abs(Math.round(anom * 100))}%</span>`);
    const weatherPulse = `
      <div class="wp-grid">
        <div class="wp">
          <div class="wp-label">Last 14 days · rainfall</div>
          <div class="wp-value" style="color:#1a4a7a;">${fmt1(wp.last14_precip_in)}<span class="unit">"</span></div>
          <div class="wp-sub">${wp.last14_gdd != null ? `${fmt0(wp.last14_gdd)} GDD accumulated` : ""}</div>
        </div>
        <div class="wp">
          <div class="wp-label">Next 14 days · forecast rain</div>
          <div class="wp-value" style="color:#b88500;">${fmt1(wp.next14_precip_in)}<span class="unit">"</span></div>
          <div class="wp-sub">${wp.next14_workable != null ? `${wp.next14_workable}/14 workable days` : ""}</div>
        </div>
        <div class="wp">
          <div class="wp-label">Season-to-date precip</div>
          <div class="wp-value" style="color:#1a4a1a;">${fmt1(wp.season_precip_in)}<span class="unit">"</span></div>
          <div class="wp-sub">${pulseAnom(wp.season_precip_anom)} vs ${fmt1(wp.season_precip_normal_in)}" normal</div>
        </div>
        <div class="wp">
          <div class="wp-label">Season-to-date GDD</div>
          <div class="wp-value" style="color:#8c3a14;">${fmt0(wp.season_gdd)}</div>
          <div class="wp-sub">${pulseAnom(wp.season_gdd_anom)} vs ${fmt0(wp.season_gdd_normal)} normal</div>
        </div>
      </div>`;

    // ── Charts (GDD + Precip cumulative, current vs 1995–2025 normal) ────────
    let chartsHtml = "";
    if (data.wx_chart && data.wx_chart.cur && data.wx_chart.normal) {
      const curPdf = data.wx_chart.cur || [], normalPdf = (data.wx_chart.normal || []).slice(0, curPdf.length);
      const xLabels = curPdf.map((o) => o.label);
      const fcFromPdf = (() => { const i = curPdf.findIndex((c) => c.fc); return i >= 0 ? i : null; })();
      chartsHtml = `
        <div class="charts">
          ${lineChartSvg({
            title: "Growing degree days — cumulative",
            sub: `WNY avg · ${year} to date vs same-window normal${fcFromPdf != null ? " · incl. 14-day forecast" : ""}`,
            xLabels,
            fcFrom: fcFromPdf,
            valFmt: (v) => Math.round(v).toLocaleString(),
            series: [
              { name: `${year}`, color: "#2d6a2d", w: 2.5, vals: curPdf.map((c) => c.gdd) },
              { name: "1995–2025 normal", color: "#94a3b8", w: 2, dash: "5,4", vals: normalPdf.map((c) => c.gdd) },
            ],
          })}
          ${lineChartSvg({
            title: 'Rainfall — cumulative (")',
            sub: `WNY avg · ${year} to date vs same-window normal${fcFromPdf != null ? " · incl. 14-day forecast" : ""}`,
            xLabels,
            fcFrom: fcFromPdf,
            valFmt: (v) => `${v.toFixed(1)}"`,
            series: [
              { name: `${year}`, color: "#2563eb", w: 2.5, vals: curPdf.map((c) => c.pr) },
              { name: "1995–2025 normal", color: "#94a3b8", w: 2, dash: "5,4", vals: normalPdf.map((c) => c.pr) },
            ],
          })}
        </div>`;
    }

    // ── County table (sorted predicted yield desc) ───────────────────────
    const sorted = counties.slice().sort((a, b) => (b.forecast_yield || 0) - (a.forecast_yield || 0));
    const errorRows = (data.counties || []).filter((c) => c.error);
    const rowFor = (c) => {
      const vsMean = c.recent_5yr_mean != null && c.forecast_yield != null ? c.forecast_yield - c.recent_5yr_mean : null;
      const vsClass = vsMean == null ? "" : vsMean >= 2 ? "pos" : vsMean <= -2 ? "neg" : "neu";
      const ds = c.drought?.dsci;
      const dsClass = ds == null ? "neu" : ds >= 200 ? "neg" : ds >= 100 ? "warn" : ds >= 50 ? "warnlo" : "pos";
      const wxFactor = c.total_wx_factor;
      const wxClass = wxFactor == null ? "neu" : wxFactor < 0.92 ? "neg" : wxFactor > 1.04 ? "pos" : "neu";
      const predBu = c.pred_acres != null && c.forecast_yield != null ? c.pred_acres * c.forecast_yield : null;
      return `<tr>
        <td class="cty">${esc(c.name)}${c.overreach ? ` <span class="ovr">⚠</span>` : ""}</td>
        <td class="num primary"><b>${fmt1(c.forecast_yield)}</b></td>
        <td class="num ${vsClass}">${fmtDelta(vsMean)}</td>
        <td class="num">${fmt1(c.baseline)}</td>
        <td class="num ${wxClass}">${wxFactor != null ? wxFactor.toFixed(3) : "—"}</td>
        <td class="num ${dsClass}">${ds != null ? ds : "—"}${c.drought?.category && c.drought.category !== "None" ? `<span class="dlbl"> ${esc(c.drought.category.split(" ")[0])}</span>` : ""}</td>
        <td class="num">${fmt0(c.pred_acres)}</td>
        <td class="num">${predBu != null ? fmt0(predBu) : "—"}</td>
      </tr>`;
    };
    const countyTable = `
      <table class="cty-table">
        <thead><tr>
          <th>County</th>
          <th class="num">Pred. yield<br/><span class="th-sub">bu/ac</span></th>
          <th class="num">vs 5-yr<br/><span class="th-sub">bu/ac</span></th>
          <th class="num">Baseline<br/><span class="th-sub">bu/ac</span></th>
          <th class="num">Wx factor<br/><span class="th-sub">mechanistic</span></th>
          <th class="num">Drought<br/><span class="th-sub">DSCI</span></th>
          <th class="num">Pred. acres</th>
          <th class="num">Pred. bushels</th>
        </tr></thead>
        <tbody>${sorted.map(rowFor).join("")}</tbody>
      </table>
      ${errorRows.length ? `<div class="errnote">Excluded — insufficient NASS yield history: ${errorRows.map((c) => esc(c.name)).join(", ")}</div>` : ""}
      ${(() => {
        // The ⚠ counties, spelled out. This is the surface a grower actually
        // holds, so the caveat has to travel with the number — a page that
        // qualifies a record yield while the PDF prints it bare is the same
        // screen-vs-PDF split this file has been bitten by before.
        const fl = sorted.filter((c) => c.overreach);
        if (!fl.length) return "";
        return `<div class="ovrnote">⚠ At the model's weather-factor ceiling: ${fl.map((c) =>
          `${esc(c.name)} ${fmt1(c.forecast_yield)}`).join(" · ")}.</div>`;
      })()}`;

    // ── County yield map (async — fetches TopoJSON + waits for d3) ──────
    const mapSvg = await buildMapSvg(counties);

    // Planting nowcast chart — same SVG used in the on-screen MI card, injected
    // verbatim so the PDF tells the same story as the page. Planting-phase only:
    // drop it once past the FPD (Jun 10), matching the hero + email brief.
    const _pdfBeforeFpd = new Date() < new Date(year, 5, 10);
    const nowcastChartSvg = (_pdfBeforeFpd && cornNowcast && cornNowcast.daily && cornNowcast.daily.length > 0)
      ? buildCornNowcastChartSvg({ surveyPoints: cornSurveyPoints, nowcast: cornNowcast, backtest: cornBacktest, year })
      : null;
    const nowcastNarrative = cornNowcast && cornNowcast.todayModeledPct != null
      ? `Last survey ${Math.round(cornNowcast.lastObsPct)}% (${cornNowcast.lastObsDate.getMonth()+1}/${cornNowcast.lastObsDate.getDate()}) → modeled today <b>~${Math.round(cornNowcast.todayModeledPct)}%</b>${cornNowcast.nextMondayProjectedPct != null ? ` → next Monday ~${Math.round(cornNowcast.nextMondayProjectedPct)}%` : ''}.`
      : null;

    // ── Dairy silage demand — explains the acres side of production vs normal ──
    const dairyHtml = data.cow_silage ? (() => {
      const d = data.cow_silage;
      const acn = (n) => (n != null ? `${Math.round(n).toLocaleString()} ac` : "—");
      const rows = [
        // The herd follows the selected region now. It used to be Western NY's
        // whatever was picked, called "WNY dairy herd" here and "NY herd" in the
        // narrative below — one number, two names, one of them wrong.
        d.currentCows != null ? [`${(MI_REGIONS[region] || {}).label || "Regional"} dairy herd`, `${Math.round(d.currentCows).toLocaleString()} cows${d.herd_growth_pct != null ? ` (${d.herd_growth_pct >= 0 ? "+" : "−"}${Math.abs(d.herd_growth_pct)}%/yr)` : ""}`] : null,
        // Shows the rate that actually drives the acres, not a heifers-per-cow
        // figure that drives nothing. The tonnage is cows × tons/cow; the heifer
        // INVENTORY ratio cancels out of it while the floor is applied, and the
        // 0.749 this used to print is a different population anyway (all heifers
        // 0-24 mo, vs the HEIFERS GE 500 LBS the ratio and the sync both mean).
        d.tonsPerCow != null ? ["Silage per cow · incl. heifer share", `${d.tonsPerCow} t/yr${d.tonsPerCowFloorApplied ? " · assumed" : " · NASS-calibrated"}`] : null,
        d.requiredAcres != null ? ["Corn silage acres needed", acn(d.requiredAcres)] : null,
        d.normalAcres != null ? ["Normal-year silage acres", acn(d.normalAcres)] : null,
        d.extraAcres != null && d.extraAcres > 0 ? ["Extra acres pulled from grain", `+${Math.round(d.extraAcres).toLocaleString()} ac`] : null,
      ].filter(Boolean);
      return `<div class="section">
        <div class="section-title"><span class="ico">🐄</span> Dairy Silage Demand</div>
        <table style="width:100%;border-collapse:collapse;font-size:13px;">
          ${rows.map(([k, v]) => `<tr><td style="padding:6px 0;color:#5a7a5a;">${esc(k)}</td><td style="padding:6px 0;text-align:right;font-weight:700;color:#1a2e1a;">${esc(v)}</td></tr>`).join("")}
        </table>
        <div style="font-size:11px;color:#60735f;margin-top:8px;line-height:1.5;">NY dairies feed a near-fixed silage tonnage, so a growing herd (cows + replacement heifers) and a stressed crop divert more corn acres to silage — fewer grain acres, lower grain production.</div>
      </div>`;
    })() : "";

    // ── Compose document ─────────────────────────────────────────────────
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>SILAS — WNY Corn Market Intelligence · ${year}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;0,700;1,400&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: 'Plus Jakarta Sans', -apple-system, sans-serif; background: #fff; color: #1a2e1a; padding: 32px 40px 24px; max-width: 880px; margin: 0 auto; line-height: 1.5; }
    @media print { body { padding: 14px 20px; max-width: none; } .no-print { display: none !important; } .section { page-break-inside: avoid; } @page { margin: 0.5in; } }
    .header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 20px; border-bottom: 3px solid #2d6a2d; margin-bottom: 22px; gap: 16px; }
    .brandmark { display: flex; align-items: center; gap: 14px; flex: 0 0 auto; }
    .brandmark .clover { height: 60px; width: auto; }
    .brandmark .wordmark { height: 33px; width: auto; display: block; }
    .brandmark .byline { font-size: 10px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #5a7a5a; margin-top: 6px; }
    .title-block { text-align: right; }
    .title { font-family: 'Lora', Georgia, serif; font-size: 26px; font-weight: 700; color: #1a2e1a; line-height: 1.15; letter-spacing: -0.01em; }
    .subtitle { font-size: 12px; color: #5a7a5a; margin-top: 6px; letter-spacing: 0.04em; text-transform: uppercase; font-weight: 600; }
    .asof { font-size: 13px; color: #1a2e1a; margin-top: 4px; }
    .print-btn { display: inline-block; margin-top: 10px; padding: 8px 16px; background: #2d6a2d; color: #fff; border: none; border-radius: 6px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; }
    .section { margin-bottom: 22px; }
    .section-title { font-size: 11.5px; font-weight: 800; color: #2d6a2d; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #d6e0d6; display: flex; align-items: center; gap: 8px; }
    .section-title .ico { font-size: 14px; }
    .kpi-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
    .kpi { background: linear-gradient(135deg, #f4f8f4 0%, #eaf2ea 100%); border: 1px solid #d6e0d6; border-radius: 10px; padding: 14px 16px; }
    .kpi-label { font-size: 10px; font-weight: 800; color: #5a7a5a; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px; }
    .kpi-value { font-size: 26px; font-weight: 800; line-height: 1.05; }
    .kpi-value .unit { font-size: 13px; font-weight: 600; color: #6b7c6b; margin-left: 3px; }
    .kpi-sub { font-size: 10.5px; color: #5a7a5a; margin-top: 4px; line-height: 1.35; }
    .summary { background: #faf9f3; border: 1px solid #e8e2c8; border-left: 4px solid #8c7a30; border-radius: 8px; padding: 16px 20px; font-family: 'Lora', Georgia, serif; font-size: 13.5px; color: #1a2e1a; line-height: 1.7; }
    .summary p { margin-bottom: 10px; }
    .summary p:last-child { margin-bottom: 0; }
    .muted-italic { color: #60735f; font-style: italic; }
    .wp-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
    .wp { background: #fff; border: 1px solid #e0e8e0; border-radius: 8px; padding: 12px 14px; }
    .wp-label { font-size: 10px; font-weight: 700; color: #5a7a5a; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; }
    .wp-value { font-size: 24px; font-weight: 800; line-height: 1.05; }
    .wp-value .unit { font-size: 13px; color: #60735f; font-weight: 600; }
    .wp-sub { font-size: 10.5px; color: #6b7c6b; margin-top: 4px; line-height: 1.35; }
    .anom { font-weight: 700; }
    .anom.pos { color: #2d6a2d; }
    .anom.neg { color: #c0392b; }
    .anom.neu { color: #6b7c6b; }
    .charts { display: grid; grid-template-columns: 1fr; gap: 16px; }
    .cty-table { width: 100%; border-collapse: collapse; font-size: 12.5px; background: #fff; border: 1px solid #d6e0d6; border-radius: 8px; overflow: hidden; }
    .cty-table th { background: #2d6a2d; color: #fff; padding: 9px 10px; font-weight: 700; text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; }
    .cty-table th.num { text-align: right; }
    .cty-table th .th-sub { font-weight: 500; font-size: 9px; color: #c5e2c5; letter-spacing: 0.02em; text-transform: none; }
    .cty-table td { padding: 7px 10px; border-bottom: 1px solid #eef3ee; }
    .cty-table tbody tr:nth-child(even) td { background: #fafdfa; }
    .cty-table tbody tr:hover td { background: #f0f6f0; }
    .cty-table td.cty { font-weight: 700; color: #1a2e1a; }
    .cty-table td.cty .ovr { color: #8a6d1f; font-size: 10px; }
    .ovrnote { margin-top: 8px; padding: 9px 11px; background: #fff6e6; border: 1px solid #e8d39a; border-radius: 6px; font-size: 10.5px; color: #6b5a1f; line-height: 1.55; }
    .cty-table td.num { text-align: right; font-variant-numeric: tabular-nums; font-family: 'Plus Jakarta Sans', sans-serif; }
    .cty-table td.primary { color: #1a4a1a; }
    .cty-table td.pos { color: #2d6a2d; font-weight: 700; }
    .cty-table td.neg { color: #c0392b; font-weight: 700; }
    .cty-table td.warn { color: #b85000; font-weight: 700; }
    .cty-table td.warnlo { color: #b88500; }
    .cty-table td.neu { color: #1a2e1a; }
    .cty-table td .dlbl { font-size: 10px; color: #6b7c6b; font-weight: 500; margin-left: 2px; }
    .errnote { font-size: 11px; color: #60735f; font-style: italic; margin-top: 8px; padding-left: 4px; }
    .disclaimer { background: #fff7e8; border: 1px solid #e6d3a3; border-radius: 8px; padding: 12px 16px; font-size: 11.5px; color: #7a5b00; line-height: 1.55; margin-top: 22px; }
    .footer { margin-top: 18px; padding-top: 14px; border-top: 1px solid #d6e0d6; display: flex; justify-content: space-between; align-items: center; font-size: 10.5px; color: #60735f; flex-wrap: wrap; gap: 8px; }
    .footer .brand { font-family: 'Lora', Georgia, serif; font-weight: 700; color: #2d6a2d; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="brandmark">
      <img class="clover" src="${CLOVER_URL}" alt="Clover Ag" onerror="this.style.display='none'"/>
      <div>
        <img class="wordmark" src="${WORDMARK_URL}" alt="SILAS" onerror="this.style.display='none'"/>
        <div class="byline">by Clover Ag Risk Management</div>
      </div>
    </div>
    <div class="title-block">
      <div class="title">New York Corn — Market Intelligence</div>
      <div class="subtitle">${year} Crop Year · Yield + Production Outlook</div>
      <div class="asof">As of ${printDate}</div>
      <button class="print-btn no-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
    </div>
  </div>

  <div class="section">
    <div class="section-title"><span class="ico">📊</span> Headline</div>
    ${seasonBarHtml}
    ${kpis}
  </div>

  ${mapSvg ? `<div class="section">
    <div class="section-title"><span class="ico">🗺️</span> County Yield Map — predicted bu/ac</div>
    ${mapSvg}
  </div>` : ""}

  ${nowcastChartSvg ? `<div class="section">
    <div class="section-title"><span class="ico">📍</span> Planting Pace — observations + weather-driven projection</div>
    ${nowcastNarrative ? `<div style="font-size:12px;color:#065f46;margin-bottom:8px;line-height:1.5;">${nowcastNarrative}</div>` : ""}
    <div style="display:flex;gap:18px;font-size:10.5px;color:#065f46;margin-bottom:6px;flex-wrap:wrap;">
      <span style="display:inline-flex;align-items:center;gap:5px;">
        <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="#f59e0b" stroke-width="2.5"/></svg>
        Survey actual
      </span>
      <span style="display:inline-flex;align-items:center;gap:5px;">
        <svg width="36" height="10"><rect x="0" y="2" width="36" height="6" fill="#10b981" fill-opacity="0.18"/><line x1="0" y1="5" x2="36" y2="5" stroke="#10b981" stroke-width="2" stroke-dasharray="3,3"/></svg>
        Weather projection &plusmn; uncertainty
      </span>
    </div>
    ${nowcastChartSvg}
    <div style="font-size:10px;color:#5a7a5a;font-style:italic;margin-top:6px;">
      Band widens with horizon (random-walk uncertainty). Re-anchors each Monday when a new survey lands.
    </div>
  </div>` : ""}

  <div class="section">
    <div class="section-title"><span class="ico">📊</span> Why This Prediction</div>
    <div class="summary">${summaryHtml}</div>
  </div>

  <div class="section">
    <div class="section-title"><span class="ico">🌤️</span> Weather Pulse — observed and forecast</div>
    ${weatherPulse}
  </div>

  ${dairyHtml}

  ${chartsHtml ? `<div class="section">
    <div class="section-title"><span class="ico">📈</span> Cumulative Growing Degree Days &amp; Rainfall</div>
    ${chartsHtml}
  </div>` : ""}

  <div class="section">
    <div class="section-title"><span class="ico">🗺️</span> County Outlook — sorted by predicted yield</div>
    ${countyTable}
  </div>

  <div class="disclaimer">
    <b>⚠</b> ${esc(data.disclaimer || "Planning estimate only. Weather and pace effects use pooled agronomic heuristics. Not an official forecast — verify against scouting before acting.")}
  </div>

  <div class="footer">
    <div class="brand">Clover Ag Risk Management</div>
    <div>Generated ${printDate} at ${generatedAt} · Western New York</div>
  </div>

  <script>
    // Wait briefly so the logo + fonts have a chance to load before auto-print
    window.addEventListener('load', function () {
      setTimeout(function () { try { window.print(); } catch(e) {} }, 600);
    });
  </script>
</body>
</html>`;

    const w = window.open("", "_blank");
    if (!w) {
      setExporting(false);
      alert("Popup blocked — allow popups for this site, then click Export again.");
      return;
    }
    writePrintWindow(w, html);
    setExporting(false);
    // `region` names the dairy herd on the PRINTED sheet. Latent today because
    // `data` is region-filtered and so changes with it — but this file already
    // records what happens when a header and its arithmetic can drift apart
    // (the brief called itself "WNY Corn" over the 39-county payload), and a
    // label on a printed document is the worst place to rely on a coincidence.
  }, [data, summary, year, exporting, cornNowcast, cornBacktest, cornSurveyPoints, region]);

  // ── Plain-English situation summary, generated by Claude on demand ─────
  // Builds a compact payload of all the data signals on the page and asks
  // for a 5-6 sentence narrative. Manual refresh only — costs an Anthropic
  // API call each time, but ALLOWED_ADMIN_EMAILS gate stops random users
  // from draining credits. The structured prompt keeps the model on topic.
  const generateSummary = useCallback(async () => {
    if (!data) return;
    setSummaryStatus("Generating…");
    try {
      const headers = { ...getApiAuthHeaders(), "Content-Type": "application/json" };
      // Build a compact data brief — no PII, just numbers + state of the model.
      // ACRE-WEIGHTED yield + 5-yr avg — IDENTICAL to the page hero (miSnapshotFromResult
      // `wavg`). Hoisted so production_decomposition reuses the exact headline numbers.
      const _rows = (data.counties || []).filter((c) => !c.error);
      const _wavg = (k) => {
        let wn = 0, wd = 0, sn = 0, ss = 0;
        for (const c of _rows) { if (c[k] == null) continue; ss += c[k]; sn++; const w = c.pred_acres > 0 ? c.pred_acres : 0; wn += c[k] * w; wd += w; }
        const v = wd > 0 ? wn / wd : (sn > 0 ? ss / sn : null);
        return v != null ? Math.round(v * 10) / 10 : null;
      };
      const _predYield = _wavg("forecast_yield");
      const _fiveYr = _wavg("recent_5yr_mean");
      // Are we past the final planting date? Once we are, planting is settled and the
      // FORWARD workability projection (workable_days_*) is irrelevant — and the model
      // kept misreading it as "N field days got the crop in." So drop it post-FPD.
      const _beforeFpd = (() => {
        const f = String(data.production?.fpd || "June 10").trim().split(/\s+/);
        const mi = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11 };
        const fd = new Date(data.year, mi[(f[0]||"june").slice(0,3).toLowerCase()] ?? 5, parseInt(f[1],10) || 10);
        return new Date() < fd;
      })();
      // Pre-digested read of how hard the planting WINDOW actually was (Apr+May precip vs
      // normal), so the model can't default to "clean spring" off a tidy final pace %. A
      // wet spring that still finished on pace = a narrow-window success, NOT an easy one.
      const _springChar = (() => {
        const tj = data.wx_pulse?.season_by_month;
        if (!tj || !tj.length) return null;
        let p = 0, np = 0;
        for (const m of tj) { if (m.month === "Apr" || m.month === "May") { p += m.precip_in || 0; np += m.precip_normal_in || 0; } }
        if (np <= 0) return null;
        const pct = Math.round((p / np) * 100);
        return {
          apr_may_precip_pct_of_normal: pct,
          read: pct >= 130 ? "WET, difficult planting season — the crop got in by catching narrow dry windows, NOT because conditions were good; a tidy final pace % means they threaded it, not that it was easy"
              : pct >= 110 ? "wetter-than-normal spring — planting worked around real delays"
              : pct <= 80 ? "dry spring — planting ran easily"
              : "near-normal spring",
        };
      })();
      const brief = {
        crop_year: data.year,
        headline: {
          predicted_yield_bu_ac: _predYield,
          five_yr_avg_bu_ac: _fiveYr,
          production_bu: data.production?.pred_bu,
          production_vs_normal_pct: data.production?.pct != null ? Math.round(data.production.pct * 100) : null,
        },
        // ── WHY THE NUMBER IS THE NUMBER. The model is literally
        // recent-5yr-avg × weather × pace. Expose each term AND its bushel impact
        // so the explainer can walk the arithmetic — "the 5-yr avg is X, weather is
        // taking off Y bu, pace Z bu → the number" — instead of hand-waving. The
        // acre-weighted factors don't multiply to the penny (weighted-product ≠
        // product-of-weighted), so the formula is framed as ≈.
        prediction_math: (() => {
          const wx = _wavg("total_wx_factor");
          const pace = data.statewide_pace?.factor;
          const base = _fiveYr;
          const buImpact = (f) => (f != null && base != null ? Math.round(base * (f - 1) * 10) / 10 : null);
          return {
            formula: "predicted_yield ≈ recent_5yr_avg × weather_factor × pace_factor",
            recent_5yr_avg_bu: base,
            weather_factor: wx != null ? Math.round(wx * 1000) / 1000 : null,
            weather_yield_impact_bu: buImpact(wx),      // + above / − below the 5-yr avg attributable to weather
            pace_factor: pace != null ? Math.round(pace * 1000) / 1000 : null,
            pace_yield_impact_bu: buImpact(pace),       // + / − from planting timing vs the potential curve
            predicted_yield_bu: _predYield,
            vs_5yr_avg_bu: (_predYield != null && base != null) ? Math.round((_predYield - base) * 10) / 10 : null,
            note: "weather_factor = the mechanistic per-cell index: season-to-date OBSERVED weather PLUS the 14-day forecast, each cell scored against its own 1991-2020 normal (<1 drags, >1 lifts). pace_factor = planting timing vs the crop's yield-potential curve. Multiply both onto the 5-yr average to get the number. The *_yield_impact_bu fields say how many bushels each term adds or removes — that IS the explanation of why the prediction sits where it does vs history.",
          };
        })(),
        // ── WHY production differs from the per-acre yield. Production vs normal is
        // NOT just the yield change — it also moves with harvested grain acres, and
        // the acres piece is PP (never planted) + corn diverted to SILAGE (dairies
        // chop more acres when the crop is stressed, to hit fixed tonnage). Forces the
        // brief to NAME the driver instead of a vague "acres story".
        production_decomposition: (() => {
          const pr = data.production;
          if (!pr || !pr.pred_bu || !pr.normal_bu) return null;
          const prodPct = Math.round((pr.pred_bu / pr.normal_bu - 1) * 1000) / 10;
          const yEff = (_predYield && _fiveYr) ? Math.round((_predYield / _fiveYr - 1) * 1000) / 10 : null;
          const acresEff = yEff != null ? Math.round((prodPct - yEff) * 10) / 10 : null;
          return {
            production_vs_normal_pct: prodPct,
            per_acre_yield_effect_pct: yEff,                              // forecast vs 5-yr-mean yield
            acres_effect_pct: acresEff,                                   // residual = fewer harvested grain acres
            prevent_plant_pct: pr.pp_pct,                                 // never-planted acres
            extra_acres_to_silage_vs_normal: data.cow_silage?.extraAcres, // dairy diverts grain→silage when stressed
            normal_silage_acres: data.cow_silage?.normalAcres,
            note: "Production-vs-normal ≈ per-acre YIELD effect + ACRES effect (they roughly add). The acres effect is NOT just prevent-plant: it is PP (never planted) PLUS extra corn acres diverted to SILAGE (dairies chop more acres to hit fixed tonnage when the crop is stressed). Late-planted GRAIN is NOT an acres cut — its drag is already in per-acre yield. If you call production an 'acres story' you MUST name and size which: how many points are PP vs silage diversion. If PP is only a couple percent, the acres gap is the silage diversion — say that explicitly, don't hand-wave 'acres'.",
          };
        })(),
        pace: {
          book_pct_planted: data.statewide_pace?.pct_planted,
          ny_state_pct_planted: data.statewide_pace?.ny_state_pct,
          five_yr_normal_pct: data.statewide_pace?.normal_pct,
          pace_factor: data.statewide_pace?.factor,
          // FPD context — these acre-mix figures are FORWARD-LOOKING projections
          // of how the season ends up, NOT acres already planted. The `late`/`PP`
          // buckets only realize on/after the FPD. Keys are named `projected_*`
          // and the FPD + today's relation to it are passed so the model never
          // describes pre-FPD projections in past tense ("went in late").
          final_planting_date: data.production?.fpd || "June 10",
          today_is_before_fpd: (() => {
            const f = String(data.production?.fpd || "June 10").trim().split(/\s+/);
            const mi = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11 };
            const fd = new Date(data.year, mi[(f[0]||"june").slice(0,3).toLowerCase()] ?? 5, parseInt(f[1],10) || 10);
            return new Date() < fd;
          })(),
          projected_pct_planted_by_fpd: data.production?.planted_by_fpd_pct,
          projected_prevent_plant_pct: data.production?.pp_pct,
          // Combined projected late-planted % (grain past FPD + silage past FPD) —
          // the bucket that pairs with projected_pct_planted_by_fpd and
          // projected_prevent_plant_pct to sum to ~100% of intended acres. Each
          // takes the same yield hit. NONE of these acres are planted late yet
          // when today_is_before_fpd is true — it's a projection of the outcome.
          projected_late_planted_pct: data.production?.late_planted_pct,
          // Breakdown if you want to mention it (rarely needed in prose)
          projected_late_grain_pct: data.production?.late_grain_pct,
          projected_late_silage_pct: data.production?.late_silage_pct,
          // How hard the planting window actually was (Apr+May precip vs normal).
          spring_character: _springChar,
          // Workability projection — a FORWARD count of plantable days. ONLY meaningful
          // during planting; nulled out post-FPD so it can't be misread as "N field days
          // got the crop in the ground" (it never was a record of past planting).
          workable_days_full: _beforeFpd ? data.statewide_pace?.workable_days_full : null,
          workable_days_partial: _beforeFpd ? data.statewide_pace?.workable_days_partial : null,
          workable_days_blocked: _beforeFpd ? data.statewide_pace?.workable_days_blocked : null,
          workability_window_days: _beforeFpd ? (data.statewide_pace?.workability_window_days || 30) : null,
          soil_surplus_pct_used: data.statewide_pace?.soil_surplus_used,
        },
        // ── RIGHT NOW — the last few days of actual weather. Lead the "where we are"
        // read off THIS, not the season-to-date averages, so the brief is never tone-deaf
        // to current conditions (e.g. warning about drying soil the day after a soaker).
        recent_conditions: data.wx_pulse ? {
          asof: data.wx_pulse.asof,
          last_3d_rain_in: data.wx_pulse.last3_precip_in,
          last_14d_rain_in: data.wx_pulse.last14_precip_in,
          last_7d_heat_vs_normal_pct: data.wx_pulse.last7_gdd_anom != null ? Math.round(data.wx_pulse.last7_gdd_anom * 100) : null,
          // most-recent observed days, WNY-avg: [{date, precip_in, tmax_f, tmin_f}]
          recent_days: data.wx_pulse.recent_daily,
          // near-term forecast rain (next 5 days), from the same grid feed
          next_5d_rain_in: Array.isArray(data.wx_pulse.next14_daily)
            ? Math.round(data.wx_pulse.next14_daily.slice(0, 5).reduce((s, d) => s + (d.p || 0), 0) * 100) / 100
            : null,
        } : null,
        weather: {
          // The SHAPE of the season so far — each month's precip + GDD vs its own
          // normal — so "where we've been" is the actual arc, not a single total.
          season_trajectory_by_month: data.wx_pulse?.season_by_month,
          season_precip_in: data.wx_pulse?.season_precip_in,
          season_precip_normal_in: data.wx_pulse?.season_precip_normal_in,
          season_precip_anom_pct: data.wx_pulse?.season_precip_anom != null ? Math.round(data.wx_pulse.season_precip_anom * 100) : null,
          season_precip_window_note: `Season totals run from ${MI_SEASON.start} — that is WEEKS BEFORE most of this corn was planted, so they INCLUDE pre-plant rain that fell on bare ground. A positive season_precip_anom_pct is NOT evidence the standing crop is well watered, and in a wet spring it is the very rain that DELAYED planting (already charged as a pace penalty — do not also book it as a moisture credit, that is double-counting one wet spring as both a cost and a benefit). For the crop's actual water supply use rain_since_plant. Use these season totals only to describe pre-plant soil recharge and the character of the planting window.`,
          season_gdd: data.wx_pulse?.season_gdd,
          season_gdd_normal: data.wx_pulse?.season_gdd_normal,
          season_gdd_anom_pct: data.wx_pulse?.season_gdd_anom != null ? Math.round(data.wx_pulse.season_gdd_anom * 100) : null,
          last_14d_rain_in: data.wx_pulse?.last14_precip_in,
          next_14d_rain_in: data.wx_pulse?.next14_precip_in,
          next_14d_workable_days: data.wx_pulse?.next14_workable,
        },
        // ── THE live moisture driver: FAO-56 soil-water bucket (book-weighted +
        // where it's biting first). This is what feeds the yield model's weather
        // factor now — narrate moisture from HERE, not early_stress.
        soil_water_bucket: (() => {
          const rows = (data.counties || []).filter((c) => c.water_balance && c.water_balance.factor != null);
          if (!rows.length) return null;
          let wn = 0, wd = 0;
          for (const c of rows) { const w = c.pred_acres > 0 ? c.pred_acres : 0; wn += c.water_balance.factor * w; wd += w; }
          const bf = wd > 0 ? wn / wd : null;
          const driest = rows.filter((c) => c.water_balance.drag_pct > 0)
            .sort((a, b) => b.water_balance.drag_pct - a.water_balance.drag_pct)
            .slice(0, 5).map((c) => ({ county: c.name, drag_pct: c.water_balance.drag_pct }));
          // Is the bucket a separate multiplier, or already inside weather_factor?
          const applied = rows.some((c) => c.water_balance.applied);
          return {
            book_factor: bf != null ? Math.round(bf * 1000) / 1000 : null,
            book_drag_pct: bf != null ? Math.round((1 - bf) * 1000) / 10 : null,
            driest_counties: driest,
            applied_as_separate_multiplier: applied,
            note: "Soil-water balance over the crop's root zone — THE state of moisture in the ground, and what the dry-down map shows. Drag % = yield haircut from soil-water deficit so far. DEEPENS with persistent dryness, RECOVERS when rain refills the profile."
              + (applied ? "" : " NOTE: applied_as_separate_multiplier is FALSE — the mechanistic weather_factor runs this same bucket internally, so the drag is ALREADY COUNTED inside weather_factor and is not multiplied again. That means the drag is REAL AND ALREADY PRICED IN — it does NOT mean the ground is wet or that moisture is a non-issue. Never say 'the soil-water bucket shows no drag' on the basis of it not being a separate term; report book_drag_pct and driest_counties as the actual soil state."),
          };
        })(),
        // ── Rain the STANDING CROP has actually caught: acre-weighted from each
        // cohort's plant week forward, vs the day-matched normal for that window.
        // THIS is the moisture-supply number to lead with, not season_precip_anom_pct.
        rain_since_plant: data.precip_since_plant ? {
          received_in: data.precip_since_plant.obs_in,
          normal_in: data.precip_since_plant.normal_in,
          pct_of_normal: data.precip_since_plant.pct_of_normal,
          anom_pct: data.precip_since_plant.anom_pct,
          note: "Acre-weighted rainfall since each acre's own plant date, vs normal for that same window. Rain that fell BEFORE an acre was planted is not that acre's water supply — it landed on bare ground and mostly ran off. If this is below 100% the crop is behind on water, EVEN IF season_precip_anom_pct (which starts 04-20, weeks pre-plant) shows a surplus. When the two disagree, this one is about the crop and the season total is not. Lead with this.",
        } : null,
        // early_stress = the EARLY cold/wet emergence signal only (minor footnote,
        // only relevant in the planting/early-growing phase). NOT the moisture driver.
        early_stress: data.early_stress ? {
          stress_days: data.early_stress.stress_days,
          recovery_days: data.early_stress.recovery_days,
          net_stress_days: data.early_stress.net_stress,
          drag_pct: data.early_stress.drag_pct,
          factor: data.early_stress.factor,
          asof: data.early_stress.asof,
        } : null,
        soil_moisture_ny_topsoil: data.signals?.soil ? {
          week: data.signals.soil.week,
          very_short_pct: data.signals.soil.very_short,
          short_pct: data.signals.soil.short,
          adequate_pct: data.signals.soil.adequate,
          surplus_pct: data.signals.soil.surplus,
          note: "NASS survey, STATEWIDE NY and weekly — it covers the whole state (including the wetter North Country and east), not the 16 WNY counties this model forecasts. Coarse, lagging, and not a rebuttal to the per-cell soil-water bucket. Context only; do not use it to argue the WNY crop has enough water.",
        } : null,
        drought_monitor: data.signals?.drought ? {
          wny_avg_dsci: data.signals.drought.wny_avg_dsci,
          max_county_dsci: data.signals.drought.max_dsci,
          note: "US Drought Monitor is a WEEKLY, county-coarse, human-authored product that LAGS developing dryness by 1-3 weeks — a crop can be drawing its soil-water bank down hard while DSCI still reads 0. A zero here is NOT evidence the ground is wet and does NOT override soil_water_bucket or rain_since_plant. It is also not applied as a yield term when the soil-water bucket is running (it would double-count). Do not lead with it, and never cite DSCI=0 as proof there is no dry story.",
        } : null,
        crop_condition_pct_ge: data.signals?.condition?.pct_good_excellent,
        analog_years: data.analog_years?.analogs?.slice(0, 3).map((a) => ({
          year: a.year, precip_diff_pct: a.precip_diff_pct, gdd_diff_pct: a.gdd_diff_pct, final_yield_bu_ac: a.final_yield,
        })),
        analog_implied_yield: data.analog_years?.implied_yield,
        cohort_summary: data.cohort_summary ? {
          planted_acres: data.cohort_summary.planted_ac,
          projected_catch_up_acres: data.cohort_summary.projected_ac,
          book_weather_factor: data.cohort_summary.book_factor,
          weeks: data.cohort_summary.weekly?.slice(0, 6).map((w) => ({
            plant_week: w.week_start, acres: w.acres,
            silk_precip_anom_pct: Math.round((w.silk_precip_anom || 0) * 100),
            silk_evidence_pct: Math.round((w.silk_evidence || 0) * 100),
            factor: w.factor,
          })),
        } : null,
        dairy_silage: data.cow_silage ? {
          tons_per_cow_yr: data.cow_silage.tonsPerCow,
          current_cow_inventory: data.cow_silage.currentCows,
          herd_growth_pct: data.cow_silage.herd_growth_pct,           // smoothed state milk-cow growth
          heifers_per_cow: data.cow_silage.heifer_per_cow,            // replacement inventory (UNH/UMD formula)
          heifer_expansion_factor: data.cow_silage.heiferExpansionFactor, // >1 = expansion lifts silage demand
          normal_silage_acres: data.cow_silage.normalAcres,
          required_silage_acres: data.cow_silage.requiredAcres,
          extra_silage_acres_demanded: data.cow_silage.extraAcres,
        } : null,
        backtest_mae_bu_ac: data.backtest?.mae,
        uncertainty: data.uncertainty ? {
          // Total σ across model factors. Yield ± 1.5σ ≈ 87% confidence band.
          // σ shrinks as the season unfolds — silking is the biggest unknown
          // today and accounts for most of the σ. Falls dramatically by August.
          sigma_total: data.uncertainty.sigma_total,
          sigma_baseline: data.uncertainty.sigma_baseline,
          sigma_pace: data.uncertainty.sigma_pace,
          sigma_early_stress: data.uncertainty.sigma_early,
          sigma_silking: data.uncertainty.sigma_silking,
          confidence_pct: data.uncertainty.confidence_pct,
          phase: data.uncertainty.phase,
          day_of_season: data.uncertainty.day_of_season,
          days_to_next_sharpening: data.uncertainty.days_to_next_sharpening,
          next_sharpening: data.uncertainty.next_sharpening,
          // The STILL-OPEN range around the central call (±1.5σ ≈ 87% band, in bu/ac).
          // The upside end is a genuinely good crop that's still very much in play —
          // most of the season (silking/fill) is unobserved, so the door is open both
          // ways, not just down. This band TIGHTENS as the season banks more days.
          confidence_band_bu_plus_minus: Math.round(data.uncertainty.sigma_total * 1.5 * 10) / 10,
          season_elapsed_pct: data.uncertainty.season_elapsed_pct,
          days_left_in_season: data.uncertainty.season_len_days != null && data.uncertainty.day_of_season != null
            ? data.uncertainty.season_len_days - data.uncertainty.day_of_season : null,
        } : null,
      };
      const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric" });
      const _briefBeforeFpd = new Date() < new Date(data.year, 5, 10); // Jun 10 FPD
      const _phaseNow = (data.uncertainty && data.uncertainty.phase) || (_briefBeforeFpd ? "Planting" : "Growing");
      const prompt = `You explain, in plain language, WHY Clover Ag's corn yield model is predicting the number it is right now — for the 16 Western New York counties, ${data.year} crop year, as of ${today}. The reader (a fellow agent or a producer) is looking at the headline yield and wants to understand what the model SEES in the season's data and the forecast that produces THIS number. You are not writing a season story or a market outlook — you are opening the hood and walking through the model's own reasoning so it explains itself.

THE MODEL IS SIMPLE AND YOU SHOULD MAKE IT FEEL SIMPLE: predicted_yield ≈ recent 5-yr average × weather_factor × pace_factor. Everything in the brief is either one of those three terms or the evidence behind them. Your job is to say where the number STARTS (the 5-yr history), then what is PUSHING IT off that baseline and by how much, grounded in the actual observations and the forecast. Use prediction_math as your spine — it hands you each factor and its bushel impact.

Write ~180-260 words of clear, plain-spoken explanation. Flowing paragraphs, conversational-but-precise, no filler. Walk this causal chain in order:

1. WHERE THE NUMBER STARTS. Open on the number itself and what it's measured against: the model begins at the recent 5-yr average (prediction_math.recent_5yr_avg_bu) — the historical baseline for these counties — and the current call is prediction_math.predicted_yield_bu, i.e. prediction_math.vs_5yr_avg_bu bu above/below that history. State it cleanly so the reader knows the anchor.

2. WHAT THE PAST-SEASON DATA IS DOING TO IT. The weather_factor (prediction_math.weather_factor, worth prediction_math.weather_yield_impact_bu bu) is the model's read of the weather the crop has ACTUALLY LIVED so far, each grid cell scored against its own 1991-2020 normal. Ground it in the real observations: season GDD vs normal (weather.season_gdd / season_gdd_normal / season_gdd_anom_pct), the soil-water bucket and where it's biting first (soil_water_bucket.book_drag_pct, driest_counties), and the last-few-days conditions (recent_conditions). Say which of these are HELPING and which are DRAGGING, and net it to the weather_yield_impact_bu.

   MOISTURE — GET THIS RIGHT, IT IS THE MOST COMMON WAY THIS BRIEF GOES WRONG:
   - The crop's water supply is rain_since_plant.pct_of_normal (acre-weighted rain since each acre's OWN plant date) and soil_water_bucket.book_drag_pct (the state of the water in the ground). These two are the moisture story. Lead with them.
   - weather.season_precip_anom_pct is measured from ${MI_SEASON.start}, WEEKS before most of this corn was planted. It includes rain that fell on BARE GROUND. It is NOT the crop's water supply. NEVER cite it as evidence the crop is well watered, and never let a positive season number talk you out of a real soil-water drag or a below-normal rain_since_plant. If the two disagree, rain_since_plant and the soil-water bucket win, and you should say plainly that the crop is behind on water even though the calendar-season total looks wet.
   - In a wet spring the pre-plant rain is ALREADY charged against the number as the pace penalty (it delayed planting). Booking that same rain a second time as a moisture CREDIT is double-counting one wet spring as both a cost and a benefit. Do not do it.
   - If soil_water_bucket.applied_as_separate_multiplier is false, the bucket's drag is already inside weather_factor. That means it is REAL and PRICED IN — it does NOT mean there is no drag. Never write "the soil-water bucket shows no drag."
   - A drought_monitor DSCI of 0 and a healthy NASS statewide topsoil reading are coarse, lagging, whole-state signals. They are NOT rebuttals to the per-cell bucket. Never use them to argue the crop has enough water.
   - The dry-down map on this same page is drawn from soil_water_bucket. If you tell the reader moisture is fine while that map is orange, you are wrong and the reader will see it. Your moisture read must match what the bucket says.

3. WHAT THE FORECAST IS DOING TO IT. The weather_factor already folds in the next ~14 days (weather.next_14d_rain_in, next_14d_workable_days, recent_conditions.next_5d_rain_in) — so tell the reader what the model is ASSUMING for the days just ahead and which way it tips the number. Rain refilling a drawn-down profile lifts it; a hot dry forecast deepens the drag. Everything past that 14-day window is genuinely UNKNOWN and the model does NOT assume it — say so.

4. PLANTING TIMING. The pace_factor (prediction_math.pace_factor, worth prediction_math.pace_yield_impact_bu bu) captures how planting timing sets yield potential. One or two sentences: if it's near 1.0 it's barely moving the number; if it's dragging, say the crop went in late enough to cap potential. (Projected acre-mix figures — projected_late_planted_pct / projected_prevent_plant_pct — are FORWARD projections when pace.today_is_before_fpd is true; write them in future/conditional tense, never as acres already planted.)

5. WHAT WOULD MOVE IT FROM HERE. Close by making the number honest: it's the middle of a still-open range (± uncertainty.confidence_band_bu_plus_minus bu/ac), the season is only ~${data.uncertainty?.confidence_pct ?? "—"}% captured, and the big unresolved input is silking-window weather (uncertainty.next_sharpening). The number moves day to day not because the crop is swinging but because that unknown stretch is shrinking — and every day banks more of the answer. Hold the door open both ways: a good silking-and-fill run still makes this a strong year.

Guidance:
- The whole point is TRANSPARENCY: show the arithmetic in words. "The 5-yr average is X; weather is taking off about Y bu because [specific observation]; planting timing is ~neutral; that lands us at Z." That IS the deliverable.
- Cross-check moisture against the recent/forecast trend before calling anything a drying story: soil_water_bucket.book_drag_pct is a season-to-date integral and LAGS — if rain just fell (recent_conditions.last_3d_rain_in) or is coming (next_5d_rain_in), the profile is refilling; say so rather than leading with drought the week of a soaker.
- Round numbers the way you'd say them aloud. Show cause → effect, don't list stats.

Hard rules:
- Use ONLY numbers from the brief. Never invent a figure. If something is null/missing, don't mention it.
- Flowing paragraphs. No bullets, no headers, no section labels, no preamble ("Here's the update").
- Pace drag % = (1 − pace.pace_factor) × 100, rounded.

Data brief:
${JSON.stringify(brief, null, 2)}

Write the explanation now.`;
      const body = {
        model: "claude-opus-4-8",
        max_tokens: 1100,   // ~250-320 word target with headroom; opus for the more polished, less rigid analyst voice
        messages: [{ role: "user", content: prompt }],
      };
      // fn=mi-summary, NOT /api/extract. Same Anthropic call, but the route accepts
      // any active portal user holding can_view_mi — the same flag that put this
      // page on their nav — instead of only the two admin emails. Michael Howlett
      // could open Market Intelligence but this button 403'd for him.
      const res = await adminApiFetch("/api/market?fn=mi-summary", { method: "POST", headers, body: JSON.stringify(body) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message || `HTTP ${res.status}`);
      const text = j.content?.[0]?.text || j.content?.[0]?.input || "(empty response)";
      setSummary(text.trim());
      setSummaryGenAt(new Date());
      setSummaryStatus("");
    } catch (e) {
      setSummaryStatus(`❌ ${e.message}`);
    }
  }, [data]);

  const card = { background: "#fff", border: "1px solid #dde7dd", borderRadius: 10, padding: 16, marginBottom: 16 };
  const tdR = { padding: "7px 12px", textAlign: "right", borderBottom: "1px solid #f0f4f0", whiteSpace: "nowrap", color: "#1a2e1a" };
  // Sortable column accessor — supports nested fields (weather.*, drought.*, early_stress.*)
  // so we can rank by any of the per-county metrics.
  const SORT_ACCESSORS = {
    name: (c) => c.name,
    forecast_yield: (c) => c.forecast_yield,
    recent_5yr_mean: (c) => c.recent_5yr_mean,
    baseline: (c) => c.baseline,
    vs_recent_mean: (c) => c.vs_recent_mean,
    pace_factor: (c) => c.pace_factor,
    weather_factor: (c) => c.weather?.factor,
    total_wx_factor: (c) => c.total_wx_factor,
    pred_prod: (c) => c.pred_prod,
    pred_acres: (c) => c.pred_acres,
    normal_prod: (c) => c.normal_prod,
    confidence: (c) => c.confidence === "med" ? 1 : 0,
    season_precip_in: (c) => c.weather?.season_precip_in,
    precip_anom: (c) => c.weather?.precip_anom,
    season_gdd: (c) => c.weather?.season_gdd,
    gdd_anom: (c) => c.weather?.gdd_anom,
    drought_dsci: (c) => c.drought?.dsci,
    early_stress_drag: (c) => c.early_stress?.drag_pct,
    dairy_cows: (c) => c.dairy_cows,
    silage_acres_needed: (c) => c.silage?.acres_needed,
    silage_extra: (c) => c.silage?.extra_acres_vs_normal,
    silage_yield_est: (c) => c.silage?.yield_est_t_per_ac,
  };
  const SORT_LABEL = {
    forecast_yield: "forecast yield (high→low)",
    pred_prod: "predicted production (high→low)",
    normal_prod: "historical production (high→low)",
    recent_5yr_mean: "5-yr avg yield (high→low)",
    baseline: "baseline yield (high→low)",
    vs_recent_mean: "vs 5-yr (high→low)",
    season_precip_in: "season rainfall — most rain first",
    precip_anom: "rainfall anomaly — wettest first",
    season_gdd: "season GDD — hottest first",
    gdd_anom: "GDD anomaly — most above-normal first",
    drought_dsci: "drought DSCI — most drought first",
    early_stress_drag: "early-stress drag — most stressed first",
    pace_factor: "pace factor (high→low)",
    weather_factor: "silking weather factor (high→low)",
    name: "county A→Z",
  };

  const rows = data?.counties ? [...data.counties].filter((c) => !c.error) : [];
  rows.sort((a, b) => {
    const accessor = SORT_ACCESSORS[sortKey] || ((c) => c[sortKey]);
    if (sortKey === "name") {
      const va = accessor(a) || "", vb = accessor(b) || "";
      return va < vb ? -1 : va > vb ? 1 : 0;
    }
    const va = accessor(a), vb = accessor(b);
    return (vb ?? -1e9) - (va ?? -1e9);   // desc by default
  });
  const errRows = data?.counties ? data.counties.filter((c) => c.error) : [];
  const wny = (() => {
    if (!rows.length) return null;
    const num = (k) => rows.map((c) => c[k]).filter((x) => x != null);
    const f = num("forecast_yield");
    // ACRE-WEIGHTED regional yield (= total bu / total acres) so the headline
    // moves in lockstep with production and doesn't over-weight small counties.
    // Falls back to simple mean where county acreage is missing.
    const wavg = (k) => {
      let wn = 0, wd = 0, sn = 0, ss = 0;
      for (const c of rows) { if (c[k] == null) continue; ss += c[k]; sn++; const w = c.pred_acres > 0 ? c.pred_acres : 0; wn += c[k] * w; wd += w; }
      return wd > 0 ? wn / wd : (sn > 0 ? ss / sn : null);
    };
    const forecast = miR1(wavg("forecast_yield"));
    const fiveYr = miR1(wavg("recent_5yr_mean"));
    return {
      forecast, fiveYr,
      trend: miR1(wavg("baseline")),
      // Book composite weather factor (acre-weighted) — the model's own total_wx_factor
      // (early-season stress × drought × silking), i.e. the "weather" term in
      // Yield = trend × weather × pace. <1 = weather dragging yield, >1 = helping.
      wxFactor: (() => { const v = wavg("total_wx_factor"); return v != null ? Math.round(v * 1000) / 1000 : null; })(),
      vs: forecast != null && fiveYr != null ? miR1(forecast - fiveYr) : null,
      lo: f.length ? miR1(Math.min(...f)) : null,
      hi: f.length ? miR1(Math.max(...f)) : null,
      n: rows.length,
      // "Weather live" = counties with a live cohort weather factor wired in
      // (status="cohort"). The other statuses — "no_weather_data" (never
      // synced) and "insufficient_data" (<3 yrs of history) — fall back to
      // factor=1 and don't count as live. Earlier code checked for "ok" /
      // "partial_fill" which the model never emits — that's why the banner
      // read 0/16 even after a successful weather sync.
      wxCov: rows.filter((c) => c.weather && c.weather.status === "cohort").length,
    };
  })();

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: MI_GREEN, fontSize: 18 }}>🌽 County Corn Yield Predictor — New York</h3>
        <div style={{ fontSize: 12, color: MI_MUTE }}>
          {isPortal
            ? "Our county-by-county corn yield estimate for New York, updated twice a day from this season's weather and planting progress. A planning estimate, not a forecast of your own fields."
            : "Forecast = 7-yr Olympic avg yield (drop high + low) × mechanistic weather index (per-cell process model vs each cell's climatology normal) × your planting-survey pace. Planning estimate."}
        </div>
      </div>

      {/* ── WHICH MODEL PRODUCED THIS NUMBER ──────────────────────────────────
          `mechActive` gates BOTH the mechanistic weather layer AND the RMA
          expected anchor. With no planting cohorts the model falls all the way
          back to the trend baseline and NOTHING THROWS — the page would show
          that forecast in exactly the same type as a full run. A frozen as-of
          backtest ran 1,170 county-checkpoints past this before the residual
          columns gave it away (2026-08-23).
          Kevin: "I would not allow the UI to quietly display the resulting trend
          forecast as though it's the same model." So the mode is on the page,
          and a degraded run says so in red before the number is read. */}
      {/* ⚠ `data` IS NULL ON FIRST RENDER, before load() resolves. The first cut
          of this block read `data.model_mode` directly and threw
          "Cannot read properties of null" into the error boundary — the whole
          Market Intelligence page replaced by "Something went wrong", live.
          check-screens still reported "1/1 clean" because the boundary rendered
          without an uncaught error and 699 chars cleared its barren threshold;
          only the screenshot showed it. Guard `data` first, every time. */}
      {isPortal && data && data.model_mode && data.model_mode.mode !== "full_mechanistic" && (
        <div style={{ background: "#fdf6e8", border: "1px solid #e8d9b0", borderLeft: "4px solid #8a5a1a", borderRadius: 8,
                      padding: "9px 14px", marginBottom: 12, fontSize: 12.5, color: "#6b4a12", lineHeight: 1.5 }}>
          Today's estimate is running on a simplified method because some of its inputs are not in yet.
          Treat it as a rough projection until they are.
        </div>
      )}
      {!isPortal && data && data.model_mode && (() => {
        const mm = data.model_mode;
        const full = mm.mode === "full_mechanistic";
        const tone = full ? { bg: "#eef5ee", bd: "#cfe0cf", fg: "#2d6a2d" }
          : mm.mode === "partial" ? { bg: "#fdf6e8", bd: "#e8d9b0", fg: "#8a5a1a" }
          : { bg: "#fdeeea", bd: "#e8c3b8", fg: "#a8371d" };
        const tick = (v) => (v === true ? "✓" : v === false ? "✗" : "—");
        const chips = [
          ["RMA anchor", mm.inputs.rma_anchor],
          ["Planting cohorts", mm.inputs.planting_cohorts],
          ["Grid weather", mm.inputs.grid_weather],
          ["SSURGO", mm.inputs.ssurgo],
          ["CDL", mm.inputs.cdl],
        ];
        return (
          <div style={{ background: tone.bg, border: `1px solid ${tone.bd}`, borderLeft: `4px solid ${tone.fg}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
              <b style={{ color: tone.fg, fontSize: 13, letterSpacing: 0.3 }}>
                {full ? "MODEL: FULL MECHANISTIC" : mm.mode === "partial" ? "⚠ MODEL PARTIALLY DEGRADED" : "⚠ MODEL DEGRADED — trend baseline only"}
              </b>
              <span style={{ fontSize: 11.5, color: MI_MUTE }}>
                {mm.counties_full}/{mm.counties_total} counties on the full path
                {mm.cohorts_n != null && <> · {mm.cohorts_n} planting cohorts</>}
                {mm.grid_cells_n != null && <> · {mm.grid_cells_n.toLocaleString()} grid cells</>}
              </span>
            </div>
            <div style={{ display: "flex", gap: "4px 14px", flexWrap: "wrap", marginTop: 6, fontSize: 11.5 }}>
              {chips.map(([label, v]) => (
                <span key={label} style={{ color: v === true ? "#2d6a2d" : v === false ? "#a8371d" : MI_MUTE }}>
                  {tick(v)} {label}{v == null ? " (not reached)" : ""}
                </span>
              ))}
            </div>
            {!full && mm.reasons.length > 0 && (
              <div style={{ fontSize: 11.5, color: tone.fg, marginTop: 6, lineHeight: 1.5 }}>
                {mm.reasons.map((r, i) => <div key={i}>· {r}</div>)}
                <div style={{ color: MI_MUTE, marginTop: 4 }}>
                  This forecast is <b>not</b> the model the accuracy figures describe. Treat it as a trend
                  projection until the missing input is restored.
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Harvest pace — NASS statewide % harvested vs its 5-year average. Reads
          nass_data only (no farm data) and ignores the region toggle by design. */}
      <HarvestPaceCard sbFetch={sbFetch} />

      <div style={{ ...card, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <strong style={{ color: MI_GREEN2 }}>Corn · crop year</strong>
        {/* Region filter — narrows the hero, the table, the map and the PDF
            together. Statewide is the honest default now that the page covers 52
            counties, but the book is the WNY 16 and that is usually the number
            being asked for. */}
        {MI_MODEL_COUNTIES.some((c) => !c.wny) && (
        <div style={{ display: "flex", border: "1px solid #ccd", borderRadius: 7, overflow: "hidden" }}>
          {Object.entries(MI_REGIONS).map(([key, r]) => (
            <button key={key} onClick={() => setRegion(key)} title={r.hint}
              style={{ padding: "6px 11px", fontSize: 12.5, fontWeight: region === key ? 800 : 600, cursor: "pointer",
                       border: "none", borderLeft: key === "wny" ? "1px solid #ccd" : "none",
                       background: region === key ? MI_GREEN : "#fff", color: region === key ? "#fff" : "#5a6f5a" }}>
              {r.label}
            </button>
          ))}
        </div>)}
        {!isPortal && region !== "ny" && MI_MODEL_COUNTIES.some((c) => !c.wny) && (
          <span style={{ fontSize: 11.5, color: "#8a6d1f", background: "#fff6e6", border: "1px solid #e8d39a", borderRadius: 6, padding: "3px 8px" }}>
            filtered — {MI_REGIONS[region].hint}
          </span>
        )}
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} style={{ padding: 6, borderRadius: 6, border: "1px solid #ccd" }}>
          {[nowYear, nowYear - 1, nowYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <label style={{ fontSize: 12, color: MI_MUTE }}>Sort
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={{ marginLeft: 5, padding: 5, borderRadius: 6, border: "1px solid #ccd" }}>
            <optgroup label="── Yield + Production ──">
              <option value="forecast_yield">Predicted yield (high→low)</option>
              <option value="pred_prod">Predicted production (high→low)</option>
              <option value="normal_prod">Historical production (high→low)</option>
              <option value="recent_5yr_mean">5-yr avg yield (high→low)</option>
              <option value="baseline">Trend baseline (high→low)</option>
              <option value="vs_recent_mean">vs 5-yr (high→low)</option>
            </optgroup>
            <optgroup label="── Rainfall ──">
              <option value="season_precip_in">Season rainfall — most rain first</option>
              <option value="precip_anom">Rainfall anomaly — wettest first</option>
            </optgroup>
            <optgroup label="── Heat (GDD) ──">
              <option value="season_gdd">Season GDD — hottest first</option>
              <option value="gdd_anom">GDD anomaly — above-normal first</option>
            </optgroup>
            <optgroup label="── Drought / Stress ──">
              <option value="drought_dsci">Drought DSCI — most drought first</option>
              <option value="early_stress_drag">Early-stress drag — most stressed first</option>
            </optgroup>
            <optgroup label="── Dairy ──">
              <option value="dairy_cows">Dairy cow inventory — most cows first</option>
              <option value="silage_acres_needed">Corn acres for silage — most first</option>
              <option value="silage_extra">Extra silage vs normal — most pressure first</option>
              <option value="silage_yield_est">Silage yield estimate (t/ac) — high to low</option>
            </optgroup>
            <optgroup label="── Model factors ──">
              <option value="pace_factor">Pace factor (high→low)</option>
              <option value="total_wx_factor">Total weather factor (high→low)</option>
              <option value="weather_factor">Silking weather factor only (high→low)</option>
            </optgroup>
            <optgroup label="── Alphabetical ──">
              <option value="name">County A→Z</option>
            </optgroup>
          </select>
        </label>
        <div style={{ flex: 1 }} />
        <button onClick={buildAndPrintMi} disabled={loading || !data || exporting}
          title="Generate a polished PDF report of the current outlook (county yield map, prediction explanation, weather pulse, GDD/precip charts, county table)"
          style={{ padding: "7px 14px", background: loading || !data || exporting ? "#9ab39a" : "#1a4a1a", color: "#fff", border: "none", borderRadius: 7, cursor: loading || !data || exporting ? "default" : "pointer", fontWeight: 700, fontSize: 13 }}>
          {exporting ? "Generating…" : "📄 Export PDF"}
        </button>
      </div>

      {loading && <div style={{ color: MI_MUTE, padding: 24 }}>Computing county estimates…</div>}
      {err && <div style={{ ...card, background: "#fbeaea", color: "#a3271f" }}>Error: {err}</div>}

      {/* An input the model needs came back empty. It still computes — every
          consumer is null-guarded — but it is answering a DIFFERENT question than
          the one with full inputs, and the difference is real: missing the
          planting-survey pace cost 11 bu/ac (157 vs 168) on 2026-08-05 with no
          error anywhere. Say it on the page rather than let the number pass as
          the model's answer. */}
      {!loading && inputGap && (
        <div style={{ ...card, background: "#fff8e6", border: "1px solid #e8d9a8", color: "#7a5c00", fontSize: 13 }}>
          ⚠️ <b>Reduced inputs</b> — the {inputGap} feed returned no rows, so this estimate is
          missing the planting-pace and per-cell weather factors and will NOT match the
          full-input figure. Treat it as indicative, not as the WNY number, and tell your agent.
        </div>
      )}

      {!loading && data && (
        <>
          {/* ── Data-freshness pills — how current each feed is, at a glance, so
              stale data never quietly passes as current. Each renders only when
              it has data (e.g. NY crop condition stays hidden until ~July). ── */}
          {(() => {
            const gd = (rawRef.current && rawRef.current.gridDaily) || [];
            let wxThrough = null;
            for (const r of gd) if (!r.is_forecast && r.obs_date && (wxThrough == null || r.obs_date > wxThrough)) wxThrough = r.obs_date;
            const pr = ((rawRef.current && rawRef.current.paceRows) || []).filter((r) => Number(r.year) === year);
            let paceWk = null;
            for (const r of pr) { const w = Number(r.week); if (paceWk == null || w > paceWk) paceWk = w; }
            const sig = data.signals || {};
            const pills = [
              wxThrough ? { i: "🌦", l: `Weather thru ${wxThrough.slice(5)}` } : null,
              paceWk != null ? { i: "🌽", l: `NASS pace wk ${paceWk}` } : null,
              sig.soil && sig.soil.week != null ? { i: "💧", l: `Soil moist wk ${sig.soil.week}` } : null,
              sig.drought && sig.drought.week != null ? { i: "🌡", l: `Drought wk ${sig.drought.week}` } : null,
              sig.condition && sig.condition.week != null && sig.condition.pct_good_excellent != null ? { i: "🌱", l: `Condition wk ${sig.condition.week}` } : null,
            ].filter(Boolean);
            if (!pills.length) return null;
            return (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 10, color: "#677167", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>Data freshness</span>
                {pills.map((p, i) => (
                  <span key={i} style={{ fontSize: 11, color: "#3a5a3a", background: "#eef3ee", border: "1px solid #d6e0d6", borderRadius: 20, padding: "2px 10px", fontWeight: 600 }}>{p.i} {p.l}</span>
                ))}
              </div>
            );
          })()}
          {wny && (
            <div style={{ ...card, background: "#1a2e1a", border: "none", color: "#fff", padding: "20px 22px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: "#9fc89f", flex: 1, textAlign: "center" }}>
                  New York · Corn · {year}
                </div>
              </div>
              {/* ── Season progress bar — Apr 20 → Oct 1, today's position + the
                  phase milestones (planting done, silking, maturity). Replaces the
                  phase chip: shows WHERE in the season we are at a glance. ── */}
              {(() => {
                const sStart = new Date(year, 3, 20);  // Apr 20
                const sEnd = (() => { const [fy, fm, fd] = miFreezeDate(year).split("-").map(Number); return new Date(fy, fm - 1, fd); })();    // the yield freeze (Oct 10) — src/miFinalCall.js
                const now = new Date();
                const span = sEnd - sStart;
                const pct = Math.max(0, Math.min(100, ((now - sStart) / span) * 100));
                const todayLabel = now.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                const phase = data.uncertainty ? data.uncertainty.phase : null;
                const xOf = (d) => Math.max(0, Math.min(100, ((d - sStart) / span) * 100));
                // Growth-stage segments (boundaries match the uncertainty-model phases),
                // colored as a season arc: soil → green growth → the make-or-break silk →
                // grain fill → mature/harvest.
                const phases = [
                  { from: sStart,                to: new Date(year, 5, 15), label: "Plant",  color: "#6b7a4a" }, // Apr20–Jun15
                  { from: new Date(year, 5, 15), to: new Date(year, 6, 20), label: "Grow",   color: "#4a9e4a" }, // –Jul20
                  { from: new Date(year, 6, 20), to: new Date(year, 7, 15), label: "Silk",   color: "#e0b341" }, // –Aug15 (critical)
                  { from: new Date(year, 7, 15), to: new Date(year, 8, 25), label: "Fill",   color: "#3f8f5a" }, // –Sep25
                  { from: new Date(year, 8, 25), to: sEnd,                  label: "Mature", color: "#c98a3a" }, // –Oct1
                ].map((p) => ({ ...p, x0: xOf(p.from), x1: xOf(p.to) }));
                return (
                  <div style={{ width: "100%", maxWidth: 580, margin: "6px auto 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 10, color: "#9fc89f", marginBottom: 6 }}>
                      <span>Apr 20</span>
                      <span style={{ color: "#e8f3e8" }}>
                        <b style={{ color: "#fff" }}>{todayLabel}</b>{phase ? <> · <b style={{ color: "#fff" }}>{phase.toUpperCase()}</b></> : null}
                      </span>
                      <span>Oct 10</span>
                    </div>
                    {/* outer (no clip) holds the today marker; inner clips the segments */}
                    <div style={{ position: "relative", height: 11 }}>
                      <div style={{ position: "absolute", inset: 0, borderRadius: 5, overflow: "hidden", background: "#2a3f2a", display: "flex" }}>
                        {phases.map((p, i) => (
                          <div key={i} title={p.label} style={{ position: "absolute", left: `${p.x0}%`, width: `${p.x1 - p.x0}%`, top: 0, height: "100%", background: p.color }} />
                        ))}
                        {/* dim the still-to-come portion of the season */}
                        {pct < 100 && <div style={{ position: "absolute", left: `${pct}%`, width: `${100 - pct}%`, top: 0, height: "100%", background: "rgba(18,30,18,0.62)" }} />}
                      </div>
                      {/* today marker */}
                      <div style={{ position: "absolute", left: `${pct}%`, top: -4, width: 3, height: 19, background: "#fff", borderRadius: 2, transform: "translateX(-50%)", boxShadow: "0 0 4px rgba(0,0,0,0.5)" }} />
                    </div>
                    {/* phase labels under each segment (past = brighter) */}
                    <div style={{ position: "relative", height: 12, marginTop: 4 }}>
                      {phases.map((p, i) => (
                        <span key={i} style={{ position: "absolute", left: `${(p.x0 + p.x1) / 2}%`, transform: "translateX(-50%)", fontSize: 9.5, letterSpacing: 0.3, fontWeight: ((p.x0 + p.x1) / 2 <= pct ? 700 : 400), color: (p.x0 + p.x1) / 2 <= pct ? "#dceadc" : "#60735f", whiteSpace: "nowrap" }}>{p.label}</span>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "10px 56px", margin: "6px 0 2px" }}>
                {/* ── AFTER THE FREEZE THE HEADLINE IS THE FROZEN CALL ──────────
                    Kevin, 2026-10-20: the prediction freezes Oct 10 so it can be
                    scored against NASS finals. The live model still renders
                    everywhere else on the page; the headline is the call. */}
                {finalCall ? (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 42, fontWeight: 800, lineHeight: 1.05 }}>{Number(finalCall.predicted_yield).toFixed(1)}<span style={{ fontSize: 16, fontWeight: 600, color: "#9fc89f" }}> bu/ac</span></div>
                  <div style={{ fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", color: "#9fc89f" }}>Final call, frozen {lblMd(finalCall.freeze_date)}</div>
                  {finalCall.five_yr_avg_yield != null && <div style={{ fontSize: 12.5, fontWeight: 700, color: finalCall.predicted_yield - finalCall.five_yr_avg_yield >= 0 ? "#7fd18b" : "#f0a3a3", marginTop: 2 }}>{miSigned(finalCall.predicted_yield - finalCall.five_yr_avg_yield, 1)} vs 5-yr avg</div>}
                  {finalCall.source_snapshot_date !== finalCall.freeze_date && <div style={{ fontSize: 11, color: "#c8d8c8", marginTop: 2 }}>last computed {lblMd(finalCall.source_snapshot_date)}</div>}
                  <div style={{ fontSize: 11, color: "#c8d8c8", marginTop: 2 }}>Scored against NASS final yields when published</div>
                </div>
                ) : (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 42, fontWeight: 800, lineHeight: 1.05 }}>{wny.forecast ?? "—"}<span style={{ fontSize: 16, fontWeight: 600, color: "#9fc89f" }}> bu/ac</span></div>
                  <div style={{ fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", color: "#9fc89f" }}>Predicted yield</div>
                  {data.uncertainty && wny.forecast != null && (() => {
                    // ±1.5σ band ≈ 87% confidence
                    const band = data.uncertainty.sigma_total * 1.5;
                    const lo = Math.max(0, wny.forecast - band).toFixed(0);
                    const hi = (wny.forecast + band).toFixed(0);
                    return (
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#c8d8c8", marginTop: 2 }} title={`±1.5σ confidence band. σ contributors: baseline ${data.uncertainty.sigma_baseline}, pace ${data.uncertainty.sigma_pace}, early-stress ${data.uncertainty.sigma_early}, silking ${data.uncertainty.sigma_silking}. Tightens as cohort silking evidence accumulates.`}>
                        Range: <b style={{ color: "#fff" }}>{lo} – {hi}</b> bu/ac
                      </div>
                    );
                  })()}
                  {wny.vs != null && <div style={{ fontSize: 12.5, fontWeight: 700, color: wny.vs >= 0 ? "#7fd18b" : "#f0a3a3", marginTop: 2 }}>{miSigned(wny.vs, 1)} vs 5-yr avg</div>}
                </div>
                )}
                {finalCall && finalCall.production_bu != null && (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 42, fontWeight: 800, lineHeight: 1.05 }}>{(finalCall.production_bu / 1e6).toFixed(1)}M<span style={{ fontSize: 16, fontWeight: 600, color: "#9fc89f" }}> bu</span></div>
                    <div style={{ fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", color: "#9fc89f" }}>Production, frozen {lblMd(finalCall.freeze_date)}</div>
                  </div>
                )}
                {data.production && !(finalCall && finalCall.production_bu != null) && (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 42, fontWeight: 800, lineHeight: 1.05 }}>{(data.production.pred_bu / 1e6).toFixed(1)}M<span style={{ fontSize: 16, fontWeight: 600, color: "#9fc89f" }}> bu</span></div>
                    <div style={{ fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", color: "#9fc89f" }}>Est. production · {data.production.counties} cty</div>
                    {data.production.pct != null && <div style={{ fontSize: 12.5, fontWeight: 700, color: data.production.pct >= 0 ? "#7fd18b" : "#f0a3a3", marginTop: 2 }}>{data.production.pct >= 0 ? "▲" : "▼"} {Math.abs(Math.round(data.production.pct * 100))}% vs 5-yr normal ({(data.production.normal_bu / 1e6).toFixed(1)}M)</div>}
                    {/* ── The acre half's own error, and it is NOT symmetric ──
                        Five of six seasons land inside +/-6.2% and 2025 is +26.4%
                        — the year the acres were chopped instead of combined. An
                        SD would average that into a tidy band that is too wide
                        for a normal year and far too narrow for a 2025, so the
                        measured spread is printed as it fell. It covers the ACRE
                        half only and says so; the yield range is the line above,
                        on the yield card. */}
                    {acreBand && (
                      <div
                        style={{ fontSize: 11, fontWeight: 600, color: "#c8d8c8", marginTop: 3 }}
                        title={`Acreage only — this says nothing about whether the yield is right.

All ${data.production.counties} counties are on the projected-acres branch (NASS has published no county harvested acreage for ${year} yet), so the acre half is a 5-year mean. Rolling-origin error of that same rule on NY state grain acres, ${acreBand.from_year}-${acreBand.to_year} (n=${acreBand.n}): MAE ${acreBand.mae_pct.toFixed(1)}%, bias ${acreBand.bias_pct >= 0 ? "+" : ""}${acreBand.bias_pct.toFixed(1)}%, worst ${acreBand.worst_pct >= 0 ? "+" : ""}${acreBand.worst_pct.toFixed(1)}% in ${acreBand.worst_year}.

Per season: ${acreBand.errors.map((e) => `${e.year} ${e.err_pct >= 0 ? "+" : ""}${e.err_pct.toFixed(1)}%`).join(" · ")}

An acreage projection that ran high means the realized crop was smaller, so the worst OVER-projection sets the LOW end.`}
                      >
                        If the acres miss like they have: <b style={{ color: "#fff" }}>{(data.production.pred_bu * acreBand.low_mult / 1e6).toFixed(1)} – {(data.production.pred_bu * acreBand.high_mult / 1e6).toFixed(1)}M</b>
                        <div style={{ fontSize: 10, fontWeight: 600, color: "#9fc89f", marginTop: 1 }}>
                          {/* ⚠ NAME THE BASIS ON SCREEN, not only in the tooltip. The band is
                              measured on the NY STATE grain-acre series — the county series is
                              too suppressed to support one — and the default view here is the
                              16-county book, so the rate is being carried across footprints.
                              That is a reasonable extension of the same projection rule and it
                              is NOT a measurement of this subtotal, which is the difference a
                              reader is entitled to see without hovering. */}
                          acre error only · NY state, {acreBand.n} seasons · worst {acreBand.worst_pct >= 0 ? "+" : ""}{acreBand.worst_pct.toFixed(0)}% in {acreBand.worst_year}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {data.season_weather_score && (() => {
                  const sr = data.season_weather_score;
                  const s = sr.score;
                  const sc = s >= 73 ? "#7fd18b" : s >= 60 ? "#bfe0a0" : s >= 48 ? "#e8e3b0" : s >= 36 ? "#f0b87a" : "#f0a3a3";
                  return (
                    <div style={{ textAlign: "center" }} title={`Season Weather Score (0-100) — how favorable the growing weather has been for corn: what's happened since Apr 20 + the forecast, projected with a neutral rest-of-season. Each day is scored vs that stage's ideal — penalizing COLD and EXCESS WATER as well as heat + drought — and weighted by how much the stage decides yield (silking ~3x a May day), per cell across the book. Anchored ~80 early (we're still before the make-or-break weather) and KEEPS ADAPTING: climbs on a good summer, drops on a bad silking. Validated: the underlying quality correlates 0.84 with actual NASS yields; best year ~90, a disaster ~38.`}>
                      <div style={{ fontSize: 42, fontWeight: 800, lineHeight: 1.05, color: sc }}>{s}<span style={{ fontSize: 16, fontWeight: 600, color: "#9fc89f" }}>/100</span></div>
                      <div style={{ fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", color: "#9fc89f" }}>Season weather</div>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: sc, marginTop: 2 }}>{sr.label}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#c8d8c8", marginTop: 2 }}>since Apr 20 · incl. forecast</div>
                    </div>
                  );
                })()}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, marginTop: 16, paddingTop: 14, borderTop: "1px solid #33513380" }}>
                {(() => {
                  // Phase switch: BEFORE the final planting date (Jun 10) the hero leads
                  // with planting metrics; AFTER, with growing-crop weather metrics. Auto-
                  // flips every Jun 10 — no manual change. 5-yr avg + trend stay in both.
                  const beforeFpd = new Date() < new Date(year, 5, 10);
                  const sp = data.statewide_pace || {};
                  const pr = data.production || {};
                  const kpis = beforeFpd ? [
                    ["5-yr avg yield", wny.fiveYr != null ? `${wny.fiveYr}` : "—", "#fff", null],
                    [`Trend → ${year}`, wny.trend != null ? `${wny.trend}` : "—", "#fff", null],
                    ["Book pace planted", sp.pct_planted != null ? `${Math.round(sp.pct_planted)}%` : "—", "#fff", "Corn % planted across your book's planting survey."],
                    ["Predicted ac by FPD", pr.planted_by_fpd_pct != null ? `${pr.planted_by_fpd_pct}%` : "—", "#fff", "Projected share of intended acres planted by the final planting date."],
                    ["Predicted PP ac", pr.pp_pct != null ? `${pr.pp_pct}%` : "—", pr.pp_pct >= 8 ? "#f0a3a3" : "#fff", "Projected prevent-plant acres (never planted)."],
                  ] : [
                    ["5-yr avg yield", wny.fiveYr != null ? `${wny.fiveYr}` : "—", "#fff", null],
                    [`Baseline → ${year}`, wny.trend != null ? `${wny.trend}` : "—", "#fff", null],
                    ["Total weather factor", wny.wxFactor != null ? `×${wny.wxFactor.toFixed(3)}` : "—",
                      wny.wxFactor == null ? "#fff" : wny.wxFactor >= 1.01 ? "#7fd18b" : wny.wxFactor <= 0.97 ? "#f0a3a3" : wny.wxFactor < 1 ? "#e0c87a" : "#fff",
                      "Mechanistic weather index: a per-cell process model (phenology, soil-water bucket, kernel-set + grain-fill) vs each cell's climatology normal, acre-weighted to the book. The 'weather' term in Yield = recent-avg × weather × pace. Below 1 = weather shaving yield; above 1 = helping."],
                    ...(data.uncertainty ? [[
                      "Info captured", `${data.uncertainty.confidence_pct}%`,
                      data.uncertainty.confidence_pct >= 70 ? "#7fd18b" : data.uncertainty.confidence_pct >= 40 ? "#e0c87a" : "#f0a3a3",
                      "Share of season information OBSERVED vs still projected. Pace 25% · early weather 15% · silking 45% · grain fill 15%.",
                    ]] : []),
                  ];
                  return kpis.map(([lb, v, color, tip], i) => (
                    <div key={i} style={{ textAlign: "center" }} title={tip || undefined}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: color || "#fff" }}>{v}</div>
                      <div style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase", color: "#9fc89f", marginTop: 1, cursor: tip ? "help" : "default" }}>{lb}</div>
                    </div>
                  ));
                })()}
              </div>
              {/* Outlook strip — the single biggest remaining swing + when the
                  picture next sharpens. Distills the σ decomposition to a glance. */}
              {data.uncertainty && (() => {
                const u = data.uncertainty;
                const contrib = [
                  { k: "silking weather", v: Number(u.sigma_silking) || 0 },
                  { k: "planting pace", v: Number(u.sigma_pace) || 0 },
                  { k: "early-season weather", v: Number(u.sigma_early) || 0 },
                  { k: "yield baseline", v: Number(u.sigma_baseline) || 0 },
                ].sort((a, b) => b.v - a.v);
                const top = contrib[0];
                const days = u.days_to_next_sharpening;
                return (
                  <div style={{ display: "flex", justifyContent: "center", gap: "8px 22px", flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: "1px solid #33513380", fontSize: 11.5, color: "#c8d8c8" }}>
                    <span>Biggest swing from here: <b style={{ color: "#fff" }}>{top.k}</b>{top.v > 0 ? <span style={{ color: "#9fc89f" }}> (±{(1.5 * top.v).toFixed(0)} bu/ac)</span> : null}</span>
                    {u.next_sharpening && <span>Picture sharpens at <b style={{ color: "#fff" }}>{u.next_sharpening}</b>{days != null ? <span style={{ color: "#9fc89f" }}> (~{days}d)</span> : null}</span>}
                  </div>
                );
              })()}
              <div style={{ fontSize: 10.5, color: "#7fa57f", textAlign: "center", marginTop: 12 }}>
                Yield = {(() => {
                  // Name the anchor actually in use. This line said "7-yr Olympic
                  // avg" while every county anchored on RMA expected.
                  const cs = data.counties || [];
                  const nR = cs.filter((c) => c.anchor_source === "rma_expected").length;
                  if (!cs.length) return "county anchor";
                  if (nR === cs.length) return "RMA expected county yield";
                  if (nR === 0) return "7-yr Olympic avg";
                  return `RMA expected (${nR}/${cs.length}) + 7-yr Olympic`;
                })()} × weather (mechanistic) × planting pace · Production = yield × (5-yr harvested acres × planted-by-FPD {(data.production && data.production.fpd) || "Jun 10"})
              </div>
            </div>
          )}

          {/* ── USDA's own forecast, printed beside ours ──────────────────────
              A benchmark, never a correction. See the note on `usdaBench`. */}
          {usdaBench && usdaBench.yield != null && data.production && (() => {
            // `production` carries NO acres field — miFilterResult rebuilds it per
            // region from county pred_prod only. Sum the counties the same way, so
            // the acreage here always matches the production above it and follows
            // the region toggle. (Dividing by a non-existent production.acres would
            // have rendered this whole card as dashes.)
            let ourAc = 0;
            for (const c of (data.counties || [])) {
              if (c.error || c.pred_acres == null) continue;
              ourAc += Number(c.pred_acres);
            }
            ourAc = ourAc > 0 ? ourAc : null;
            const ourP = data.production.pred_bu;
            const ourY = ourAc ? ourP / ourAc : null;
            const dY = ourY != null && usdaBench.yield != null ? ourY - usdaBench.yield : null;
            const dP = ourP != null && usdaBench.prod != null ? ourP - usdaBench.prod : null;
            const cond = data.signals && data.signals.condition ? data.signals.condition.pct_good_excellent : null;
            const cell = { padding: "7px 10px", borderBottom: "1px solid #eef3ee", fontVariantNumeric: "tabular-nums" };
            const num = (v, d) => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
            return (
              <div style={{ background: "#fff", border: "1px solid #dde7dd", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 800, color: "#1a2e1a" }}>⚖️ Beside USDA — {usdaBench.year} New York corn for grain</span>
                  <span style={{ fontSize: 11, color: "#60735f" }}>
                    NASS {usdaBench.isForecast ? "forecast" : "final"} · statewide
                  </span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5, minWidth: 480 }}>
                    <thead>
                      <tr style={{ color: "#60735f", textAlign: "right" }}>
                        <th style={{ ...cell, textAlign: "left" }}></th>
                        <th style={{ ...cell, textAlign: "right" }}>Yield bu/ac</th>
                        <th style={{ ...cell, textAlign: "right" }}>Harvested ac</th>
                        <th style={{ ...cell, textAlign: "right" }}>Production</th>
                      </tr>
                    </thead>
                    <tbody style={{ textAlign: "right" }}>
                      <tr>
                        <td style={{ ...cell, textAlign: "left", fontWeight: 700, color: "#2d6a2d" }}>{isPortal ? "Our estimate" : "Silas"} · {data.production.counties} {isPortal ? "counties" : "cty"}</td>
                        <td style={cell}>{num(ourY, 1)}</td>
                        <td style={cell}>{ourAc ? Math.round(ourAc).toLocaleString() : "—"}</td>
                        <td style={cell}>{num(ourP / 1e6, 2)}M</td>
                      </tr>
                      <tr>
                        <td style={{ ...cell, textAlign: "left", fontWeight: 700, color: "#35597b" }}>USDA · statewide</td>
                        <td style={cell}>{num(usdaBench.yield, 1)}</td>
                        <td style={cell}>{usdaBench.acres ? Math.round(usdaBench.acres).toLocaleString() : "—"}</td>
                        <td style={cell}>{usdaBench.prod != null ? num(usdaBench.prod / 1e6, 2) + "M" : "—"}</td>
                      </tr>
                      {/* ⚠ ACRES AND PRODUCTION ONLY SUBTRACT WHEN THE FOOTPRINTS MATCH.
                          Yield is per-acre and survives a footprint difference; TOTALS do
                          not. On Western NY (16 counties, ~60% of state acres) the raw
                          subtraction reads "-215,683 ac / -12.27M bu", which is almost
                          entirely counties we do not model — a footprint gap rendered as
                          a disagreement, and the more alarming number of the two. Below
                          95% coverage those two cells say what they are instead. */}
                      {(() => {
                        const cov = ourAc && usdaBench.acres ? ourAc / usdaBench.acres : null;
                        const comparable = cov != null && cov >= 0.95;
                        const dCell = { ...cell, color: "#1a2e1a" };
                        const naCell = { ...cell, color: "#677167", fontWeight: 600, fontSize: 11 };
                        return (
                          <tr style={{ fontWeight: 700 }}>
                            <td style={{ ...cell, textAlign: "left", color: "#1a2e1a" }}>Difference</td>
                            <td style={dCell}>{dY == null ? "—" : miSigned(dY, 1)}</td>
                            {comparable ? (
                              <>
                                <td style={dCell}>{(ourAc - usdaBench.acres >= 0 ? "+" : "") + Math.round(ourAc - usdaBench.acres).toLocaleString()}</td>
                                <td style={dCell}>{dP == null ? "—" : (dP >= 0 ? "+" : "") + num(dP / 1e6, 2) + "M"}</td>
                              </>
                            ) : (
                              <td colSpan={2} style={{ ...naCell, textAlign: "right" }}>
                                not comparable — {cov != null ? Math.round(cov * 100) : "?"}% of state acres modelled
                              </td>
                            )}
                          </tr>
                        );
                      })()}
                      {usdaBench.normYield != null && (
                        <tr style={{ color: "#60735f" }}>
                          <td style={{ ...cell, textAlign: "left", borderBottom: 0 }}>NY {usdaBench.normN}-yr normal</td>
                          <td style={{ ...cell, borderBottom: 0 }}>{num(usdaBench.normYield, 1)}</td>
                          <td style={{ ...cell, borderBottom: 0 }}>{Math.round(usdaBench.normAcres).toLocaleString()}</td>
                          <td style={{ ...cell, borderBottom: 0 }}>{num(usdaBench.normProd / 1e6, 2)}M</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 8, lineHeight: 1.55 }}>
                  <b style={{ color: "#5a6a5a" }}>Not an apples-to-apples footprint.</b> Silas models {data.production.counties} counties; USDA's figure is all of New York.
                  Those counties have carried 77–93% of state grain acres historically, so expect our acreage to sit below USDA's — where it doesn't, that is worth a look on its own.
                  {" "}<b style={{ color: "#5a6a5a" }}>Harvested-grain acreage is the softest number here</b> — the grain/silage split is decided at harvest, and NASS revised its own 2025 acreage from 620k (June) to 545k (Aug) to 450k (final).
                  {cond != null && <> {" "}<b style={{ color: "#5a6a5a" }}>Independent check:</b> NASS rates the NY crop <b>{cond}% good/excellent</b> this week — its own field survey, published alongside the yield forecast.</>}
                  {" "}The two are shown side by side and not reconciled: if they disagree, that <i>is</i> the finding.
                </div>
              </div>
            );
          })()}


          {/* ── Corn planting nowcast — weather-driven projection chart ─────
              Solid orange line = your survey observations. Dotted green line
              + shaded band = where pace is heading day-by-day from the most
              recent Monday survey, projected through end of season using
              the workability + willingness model the yield page above uses.
              Band width = backtest MAE (your own book) × sqrt(days_ahead/7),
              defaulting to ±5 pts until enough surveys have been logged.
              No jargon — the chart tells the story. ── */}
          {false && cornNowcast && cornNowcast.daily && cornNowcast.daily.length > 0 && (() => {
            const c = cornNowcast;
            const baseMAE = (cornBacktest && cornBacktest.n >= 3) ? cornBacktest.maePctPts : 5;
            const svgStr = buildCornNowcastChartSvg({ surveyPoints: cornSurveyPoints, nowcast: c, backtest: cornBacktest, year });
            if (!svgStr) return null;
            return (
              <div style={{
                background:"#f0fdf4", border:"1px solid #bbf7d0", borderLeft:"4px solid #10b981",
                borderRadius:10, padding:"14px 16px", marginBottom:14,
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:8, marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:800, color:"#047857", letterSpacing:"0.08em", textTransform:"uppercase" }}>
                      📍 Planting Pace — book observations + weather-driven projection
                    </div>
                    <div style={{ fontSize:12, color:"#065f46", marginTop:3, lineHeight:1.5 }}>
                      Latest survey <b>{Math.round(c.lastObsPct)}%</b> ({c.lastObsDate.getMonth()+1}/{c.lastObsDate.getDate()}). Weather-driven projection forward: {c.nextMondayProjectedPct != null ? <>~<b>{Math.round(c.nextMondayProjectedPct)}%</b> next Monday</> : null}.
                    </div>
                    {cornStaleAdj && cornStaleAdj.staleN > 0 && (
                      <div style={{ fontSize:11, color:"#5a7a5a", marginTop:4 }}>
                        <b style={{color:"#047857"}}>{cornStaleAdj.freshN}</b> reporting fresh · <b style={{color:"#b88500"}}>{cornStaleAdj.staleN}</b> haven't reported in {'>'} {cornStaleAdj.freshDaysThreshold} day{cornStaleAdj.freshDaysThreshold===1?'':'s'}{cornStaleAdj.neverN > 0 ? <> · <b style={{color:"#9ca3af"}}>{cornStaleAdj.neverN}</b> never reported</> : null} — true pace today may already be higher than the latest survey.
                        {cornStaleAdj.staleDetail.length > 0 && (
                          <span title={cornStaleAdj.staleDetail.map(s => `${s.name} — last reported ${s.daysStale}d ago, ${Math.round(s.lastPlanted)}/${Math.round(s.intended)} ac`).join('\n')} style={{ marginLeft:8, cursor:"help", textDecoration:"underline dotted" }}>
                            (hover for stale list)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:14, fontSize:10.5, color:"#065f46", flexWrap:"wrap" }}>
                    <span style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="#f59e0b" strokeWidth="2.5"/></svg>
                      Survey actual
                    </span>
                    <span style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <svg width="36" height="10">
                        <rect x="0" y="2" width="36" height="6" fill="#10b981" fillOpacity="0.18"/>
                        <line x1="0" y1="5" x2="36" y2="5" stroke="#10b981" strokeWidth="2" strokeDasharray="3,3"/>
                      </svg>
                      Weather projection ± uncertainty
                    </span>
                  </div>
                </div>
                <div dangerouslySetInnerHTML={{ __html: svgStr }} />
                <div style={{ fontSize:10, color:"#5a7a5a", fontStyle:"italic", marginTop:6 }}>
                  Band ±{baseMAE.toFixed(1)} pts {cornBacktest && cornBacktest.n >= 3 ? `(model accuracy from your last ${cornBacktest.n} surveys)` : "(default — calibrates after a few Mondays)"} · widens with horizon
                </div>
              </div>
            );
          })()}

          {/* ── Prediction over time — daily history of the model's call ──
              How the predicted yield has evolved over the season. Accrues from
              the day this shipped (no backfill of past predictions). ── */}
          {yieldHistory && yieldHistory.length > 0 && (() => {
            // The series ENDS at the freeze (Oct 10). Rows written after it before
            // the rule existed (2026-10-11 .. 10-17) stay in the table — deleting
            // them is irreversible — and are clipped here instead.
            const h = miClipToFreeze(yieldHistory, year).filter((r) => r.predicted_yield != null);
            if (!h.length) return null;
            const lbl = (d) => { const p = String(d).split("-"); return `${+p[1]}/${+p[2]}`; };
            const xL = h.map((r) => lbl(r.snapshot_date));
            const first = h[0], last = h[h.length - 1];
            const delta = (last.predicted_yield != null && first.predicted_yield != null) ? last.predicted_yield - first.predicted_yield : null;
            return (
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef3ee", display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
                  <span style={{ fontWeight: 800, color: "#1a2e1a" }}>📈 Prediction over time — {year} · {MI_REGIONS[region].label}</span>
                  {delta != null && h.length > 1 && <span style={{ fontSize: 12, color: "#5a7258" }}>{xL[0]}: <b>{Math.round(first.predicted_yield)}</b> → {xL[xL.length - 1]}: <b>{Math.round(last.predicted_yield)}</b> bu/ac <b style={{ color: delta >= 0 ? "#2f7d32" : "#b85000" }}>({delta >= 0 ? "+" : ""}{delta.toFixed(1)})</b></span>}
                </div>
                <div style={{ padding: 14 }}>
                  {/* WHERE THIS SERIES STARTS IS PART OF THE SERIES. The county
                      set changed on 2026-08-13 (16 -> 39), and a past day cannot
                      be honestly re-run for a different set — the model reads
                      weather as of now. So All New York genuinely has no history
                      before then, and saying so beats drawing a short line that
                      looks like the season began in August. */}
                  {/* A STEP IN THIS LINE IS NOT ALWAYS WEATHER. Replaying the
                      model over settled data moves it 0.12 bu/day; the logged
                      season moved 2.28. The difference was the model being
                      CHANGED underneath the series — the weather ceiling alone
                      moved twice in August. So when the series spans more than
                      one model version, say where the change is instead of
                      leaving it to read as a forecast losing its nerve. */}
                  {(() => {
                    // A NULL version IS a distinct version — "not recorded". The
                    // first cut of this required two non-null versions and so
                    // stayed silent on the one transition it was built for: the
                    // day versioning began, which is also the day the response
                    // curve landed and moved the book ~8 bu. Comparing raw values
                    // (null included) is what makes the boundary visible.
                    const chg = h.filter((r, i) => i > 0 && (r.model_version || null) !== (h[i - 1].model_version || null));
                    if (!chg.length) return null;
                    const known = chg.filter((r) => r.model_version);
                    const unknown = chg.filter((r) => !r.model_version);
                    return (
                      <div style={{ fontSize: 11.5, color: "#8a6d1f", background: "#fff6e6", border: "1px solid #e8d39a", borderRadius: 5, padding: "5px 8px", marginBottom: 8 }}>
                        {isPortal
                          ? <>↺ We updated how this estimate is calculated on {chg.map((r) => lbl(r.snapshot_date)).join(", ")} — the step there comes from that, not the weather.</>
                          : <>⚠ The model changed mid-series on {chg.map((r) => lbl(r.snapshot_date)).join(", ")} — a step there is the model, not the weather.</>}
                        {!isPortal && known.length > 0 && unknown.length === 0 && h.some((r) => !r.model_version) &&
                          " Days before that ran under a version nobody recorded, so their steps cannot be attributed either way."}
                      </div>
                    );
                  })()}
                  {h.length > 1 && first.snapshot_date > `${year}-07-01` && (
                    <div style={{ fontSize: 11.5, color: MI_MUTE, marginBottom: 8 }}>
                      Series starts {lbl(first.snapshot_date)} — {MI_REGIONS[region].label} has only been modelled since then.
                      {region === "ny" && " The 23 eastern counties joined on 8/13; earlier days were computed over the Western NY 16 and are charted under that toggle, not restated here."}
                    </div>
                  )}
                  {h.length < 2 ? (
                    <div style={{ fontSize: 12, color: MI_MUTE, fontStyle: "italic" }}>Daily logging just started for {MI_REGIONS[region].label} — today's prediction is the first point. The season trend line fills in as days accrue; check back tomorrow.</div>
                  ) : (
                    <MiChart title="Predicted yield (acre-weighted, bu/ac)" sub={`${year} model call by day · ${MI_REGIONS[region].label} · vs 5-yr avg`} xLabels={xL} fitY fmtVal={(v) => `${Math.round(v)}`} series={[
                      { name: "Predicted yield", color: "#2d6a2d", w: 2.5, vals: h.map((r) => r.predicted_yield) },
                      { name: "5-yr avg", color: "#94a3b8", dash: "5,4", vals: h.map((r) => r.five_yr_avg_yield) },
                      ...(finalCall ? [{ name: `Final call (${lblMd(finalCall.freeze_date)})`, color: "#b85000", dash: "2,3", w: 1.5, vals: h.map(() => Number(finalCall.predicted_yield)) }] : []),
                    ]} />
                  )}
                  {finalCall && (
                    <div style={{ fontSize: 11.5, color: "#5a7258", marginTop: 6 }}>
                      ● Final call frozen {lblMd(finalCall.freeze_date)}: <b>{Number(finalCall.predicted_yield).toFixed(1)} bu/ac</b>. The prediction stopped updating then so it can be scored against NASS final yields.
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── How the final call scored — frozen call vs NASS FINAL yields ──
              Built now, empty until NASS publishes (state final ~Jan, county
              finals ~Feb). Only stat_type YIELD is a final; the in-season
              forecasts are stored as YIELD_FORECAST and never score the call.
              WNY is the acre-weighted mean of the WNY counties' finals. ── */}
          {finalCall && finalScore && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef3ee" }}>
                <span style={{ fontWeight: 800, color: "#1a2e1a" }}>🎯 How the final call scored — {year}</span>
              </div>
              <div style={{ padding: 14, fontSize: 12.5, color: "#1a2e1a" }}>
                {!finalScore.available ? (
                  <div style={{ color: MI_MUTE, fontStyle: "italic" }}>{finalScore.absence}</div>
                ) : (
                  <>
                    <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 560 }}>
                      <thead><tr style={{ color: MI_MUTE, fontSize: 11, textAlign: "left" }}><th style={{ padding: "4px 6px" }}></th><th style={{ padding: "4px 6px" }}>Final call</th><th style={{ padding: "4px 6px" }}>NASS final</th><th style={{ padding: "4px 6px" }}>Miss</th></tr></thead>
                      <tbody>
                        <tr><td style={{ padding: "4px 6px" }}>New York (state)</td><td style={{ padding: "4px 6px" }}>{finalScore.ny.frozen != null ? Number(finalScore.ny.frozen).toFixed(1) : "—"}</td><td style={{ padding: "4px 6px" }}>{finalScore.ny.nass_state_final ?? "not published"}</td><td style={{ padding: "4px 6px" }}>{finalScore.ny.error_vs_state != null ? miSigned(finalScore.ny.error_vs_state, 1) : "—"}</td></tr>
                        <tr><td style={{ padding: "4px 6px" }}>Western NY</td><td style={{ padding: "4px 6px" }}>{finalScore.wny.frozen != null ? Number(finalScore.wny.frozen).toFixed(1) : "—"}</td><td style={{ padding: "4px 6px" }}>{finalScore.wny.nass_weighted ?? "not published"}</td><td style={{ padding: "4px 6px" }}>{finalScore.wny.error != null ? miSigned(finalScore.wny.error, 1) : "—"}</td></tr>
                      </tbody>
                    </table>
                    <div style={{ fontSize: 11, color: MI_MUTE, marginTop: 6 }}>
                      bu/ac. The state final covers every county; the call covers the {MI_MODEL_COUNTIES.length} modelled counties. Western NY's NASS figure is the acre-weighted average of its counties' final yields ({finalScore.wny.used} of {MI_WNY_FIPS.size} published{finalScore.wny.missing.length ? `; not published: ${finalScore.wny.missing.length}` : ""}).
                    </div>
                    {finalScore.counties.some((c) => c.nass_final != null) && (
                      <div style={{ marginTop: 10 }}>
                        {finalScore.county_detail_captured_late && <div style={{ fontSize: 11, color: "#8a6d1f", marginBottom: 4 }}>County calls were recorded after {lblMd(finalCall.freeze_date)} (the model keeps only its latest run); the regional calls above are exact.</div>}
                        <table style={{ borderCollapse: "collapse", fontSize: 11.5 }}>
                          <thead><tr style={{ color: MI_MUTE, textAlign: "left" }}><th style={{ padding: "2px 6px" }}>County</th><th style={{ padding: "2px 6px" }}>Call</th><th style={{ padding: "2px 6px" }}>NASS final</th><th style={{ padding: "2px 6px" }}>Miss</th></tr></thead>
                          <tbody>{finalScore.counties.map((c) => (
                            <tr key={c.fips}><td style={{ padding: "2px 6px" }}>{c.county}</td><td style={{ padding: "2px 6px" }}>{c.pred_yield != null ? Number(c.pred_yield).toFixed(1) : "—"}</td><td style={{ padding: "2px 6px" }}>{c.nass_final ?? "not published"}</td><td style={{ padding: "2px 6px" }}>{c.error != null ? miSigned(c.error, 1) : "—"}</td></tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Situation Summary (LLM-written, manual refresh) ──────────
              Sends a compact data brief to Claude via /api/market?fn=mi-summary
              and renders the 5-6 sentence narrative. Costs an API call per
              refresh. Empty by default — user clicks "Generate summary".

              The button is deliberately NOT gated in the UI: reaching this page
              already requires can_view_mi, and fn=mi-summary accepts exactly that
              flag. It used to post to /api/extract, which is admin-only, so the
              button rendered for every MI grantee and then failed on click —
              which is how it looked to Michael Howlett. ── */}
          <div style={{ ...card, padding: 0, overflow: "hidden", borderColor: "#c8d8c8" }}>
            <div style={{ padding: "12px 16px", background: "linear-gradient(90deg, #f0f6f0 0%, #e7f0e7 100%)", borderBottom: summary || summaryStatus ? "1px solid #d6e0d6" : "none", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontWeight: 800, color: "#1a2e1a" }}>📊 Why This Prediction</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {summaryGenAt && <span style={{ fontSize: 10.5, color: MI_MUTE }}>Generated {summaryGenAt.toLocaleTimeString()}</span>}
                <button onClick={generateSummary} disabled={summaryStatus === "Generating…"}
                  style={{ padding: "6px 12px", background: summaryStatus === "Generating…" ? "#9ab39a" : MI_GREEN, color: "#fff", border: "none", borderRadius: 6, cursor: summaryStatus === "Generating…" ? "default" : "pointer", fontWeight: 700, fontSize: 11.5 }}>
                  {summaryStatus === "Generating…" ? "Writing…" : (summary ? "🔄 Regenerate" : "✨ Generate summary")}
                </button>
              </div>
            </div>
            {summary && (
              <div style={{ padding: "14px 18px", fontSize: 13.5, color: "#1a2e1a", lineHeight: 1.7, fontFamily: "'Lora', Georgia, serif" }}>
                {summary.split("\n\n").map((p, i) => <p key={i} style={{ margin: i === 0 ? "0 0 10px 0" : "0 0 10px 0" }}>{p}</p>)}
              </div>
            )}
            {!summary && summaryStatus && (
              <div style={{ padding: "14px 18px", fontSize: 12.5, color: MI_MUTE, fontStyle: "italic" }}>{summaryStatus}</div>
            )}
            {!summary && !summaryStatus && (
              <div style={{ padding: "12px 18px", fontSize: 11.5, color: MI_MUTE }}>
                Click <b>Generate summary</b> for a plain-language explanation of <i>why the model predicts the number it does</i> — it walks the arithmetic (5-yr average × weather × pace), grounds each factor in what the season's data and the 14-day forecast actually show, and says what could still move it.{isPortal ? "" : " Uses Claude to read the model's own inputs back to you."}
              </div>
            )}
          </div>

          {/* ── Live Signals — drought monitor, soil moisture, crop condition.
              Three independent third-party signals to corroborate (or
              contradict) the model. Drought map: official NDMC PNG for NY,
              embedded directly with a cache-buster. Soil moisture: latest
              NASS state-level %VS/S/A/Sp. Crop condition: latest NASS %G/E
              (NASS scores corn from ~late June once it's emerged). USDM,
              condition, and soil moisture now auto-sync on the NASS weekly
              crons (Tue/Thu); 'Refresh signals' is a manual force-pull. ── */}
          {data.signals && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef3ee", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontWeight: 800, color: "#1a2e1a" }}>📡 Live Signals — independent of our model</span>
                <button onClick={refreshSignals} disabled={signalsRefreshing}
                  style={{ padding: "6px 12px", background: signalsRefreshing ? "#9ab39a" : "#2d6a2d", color: "#fff", border: "none", borderRadius: 6, cursor: signalsRefreshing ? "default" : "pointer", fontWeight: 700, fontSize: 11.5 }}>
                  {signalsRefreshing ? "Refreshing..." : "🔄 Refresh signals"}
                </button>
              </div>
              {signalsRefreshMsg && <div style={{ padding: "8px 16px", fontSize: 11, color: MI_MUTE, borderBottom: "1px solid #f0f4f0" }}>{signalsRefreshMsg}</div>}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr 1fr", gap: 0 }}>
                {/* ── Drought Monitor map ─────────────────────────────────── */}
                <div style={{ padding: "14px 16px", borderRight: isMobile ? "none" : "1px solid #f0f4f0", borderBottom: isMobile ? "1px solid #f0f4f0" : "none" }}>
                  <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#5a4a3a", fontWeight: 800, marginBottom: 6 }}>🌵 Drought Monitor (USDM)</div>
                  {/* Official NDMC PNG for NY — uses a cache-buster keyed to a
                      Thursday-anchored week so the browser re-fetches when a
                      new map is released. */}
                  {(() => {
                    const t = new Date();
                    const dow = t.getDay();
                    const lastThu = new Date(t); lastThu.setDate(lastThu.getDate() - ((dow - 4 + 7) % 7));
                    const ymd = `${lastThu.getFullYear()}${String(lastThu.getMonth() + 1).padStart(2, "0")}${String(lastThu.getDate()).padStart(2, "0")}`;
                    // UNL retired StateDroughtMap.aspx?NY (404s) and the ?NY query is
                    // no longer honored on the new StateDroughtMonitor page (renders
                    // the national map). The per-state PNG is the only confirmed-
                    // working NY-specific asset, so the link opens the full NY map
                    // image rather than a dead/national page.
                    return (
                      <a href={`https://droughtmonitor.unl.edu/data/png/current/current_ny_trd.png?v=${ymd}`} target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none" }}>
                        <img src={`https://droughtmonitor.unl.edu/data/png/current/current_ny_trd.png?v=${ymd}`}
                          alt="NY US Drought Monitor"
                          style={{ width: "100%", maxHeight: 240, objectFit: "contain", borderRadius: 6, border: "1px solid #e4ece4", display: "block", background: "#fff" }}
                          onError={(e) => { e.target.style.display = "none"; }} />
                      </a>
                    );
                  })()}
                  {data.signals.drought ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, color: MI_MUTE, marginBottom: 4 }}>WNY 16-county avg DSCI · wk {data.signals.drought.week}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span style={{ fontSize: 26, fontWeight: 800, color: data.signals.drought.wny_avg_dsci >= 200 ? "#c0392b" : data.signals.drought.wny_avg_dsci >= 100 ? "#b85000" : data.signals.drought.wny_avg_dsci >= 50 ? "#b08900" : "#2d6a2d" }}>{data.signals.drought.wny_avg_dsci}</span>
                        <span style={{ fontSize: 11, color: MI_MUTE }}>(0=none · 500=D4 ubiquitous)</span>
                      </div>
                      <div style={{ fontSize: 11, color: MI_MUTE, marginTop: 2 }}>
                        Max county: <b style={{ color: "#1a2e1a" }}>{data.signals.drought.max_dsci}</b> · {data.signals.drought.county_n}/16 reporting
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, fontSize: 11, color: MI_MUTE, fontStyle: "italic" }}>
                      Click "🔄 Refresh signals" to pull per-county DSCI from NDMC.
                    </div>
                  )}
                </div>
                {/* ── Soil Moisture ────────────────────────────────────────── */}
                <div style={{ padding: "14px 16px", borderRight: isMobile ? "none" : "1px solid #f0f4f0", borderBottom: isMobile ? "1px solid #f0f4f0" : "none" }}>
                  <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#3a5a8a", fontWeight: 800, marginBottom: 6 }}>💧 NY Topsoil Moisture (NASS)</div>
                  {data.signals.soil ? (() => {
                    const s = data.signals.soil;
                    const vs = s.very_short, sh = s.short, ad = s.adequate, sp = s.surplus;
                    const sum = Math.max(1, vs + sh + ad + sp);
                    return (
                      <div>
                        <div style={{ fontSize: 11, color: MI_MUTE, marginBottom: 6 }}>State-level · wk {s.week}</div>
                        {/* Stacked bar */}
                        <div style={{ display: "flex", height: 28, borderRadius: 4, overflow: "hidden", border: "1px solid #d7e0d7", marginBottom: 8 }}>
                          {vs > 0 && <div title={`Very short ${vs}%`} style={{ width: `${(vs / sum) * 100}%`, background: "#8b3a14", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700 }}>{vs >= 8 ? `${vs}` : ""}</div>}
                          {sh > 0 && <div title={`Short ${sh}%`} style={{ width: `${(sh / sum) * 100}%`, background: "#d97264", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700 }}>{sh >= 8 ? `${sh}` : ""}</div>}
                          {ad > 0 && <div title={`Adequate ${ad}%`} style={{ width: `${(ad / sum) * 100}%`, background: "#4a8b4a", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 800 }}>{ad >= 12 ? `${ad}%` : ""}</div>}
                          {sp > 0 && <div title={`Surplus ${sp}%`} style={{ width: `${(sp / sum) * 100}%`, background: "#4a8eb8", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700 }}>{sp >= 8 ? `${sp}` : ""}</div>}
                        </div>
                        <div style={{ fontSize: 10.5, color: MI_MUTE, display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#8b3a14", fontWeight: 700 }}>VShort {vs}</span>
                          <span style={{ color: "#a35040", fontWeight: 700 }}>Short {sh}</span>
                          <span style={{ color: "#3a6e3a", fontWeight: 700 }}>Adequate {ad}</span>
                          <span style={{ color: "#2d6a8a", fontWeight: 700 }}>Surplus {sp}</span>
                        </div>
                        <div style={{ fontSize: 11, color: MI_MUTE, marginTop: 10 }}>
                          Sum: <b style={{ color: "#1a2e1a" }}>{vs + sh}%</b> drier than ideal · <b style={{ color: "#1a2e1a" }}>{sp}%</b> waterlogged
                        </div>
                      </div>
                    );
                  })() : (
                    <div style={{ fontSize: 11, color: MI_MUTE, fontStyle: "italic", marginTop: 8 }}>
                      No soil-moisture data yet — click "Refresh signals" to pull.
                    </div>
                  )}
                </div>
                {/* ── Crop Condition ───────────────────────────────────────── */}
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#3a6e3a", fontWeight: 800, marginBottom: 6 }}>🌽 NY Crop Condition (NASS)</div>
                  {data.signals.condition && data.signals.condition.pct_good_excellent != null ? (
                    <div>
                      <div style={{ fontSize: 11, color: MI_MUTE, marginBottom: 4 }}>%Good + %Excellent · wk {data.signals.condition.week}</div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: data.signals.condition.pct_good_excellent >= 70 ? "#2d6a2d" : data.signals.condition.pct_good_excellent >= 50 ? "#b08900" : "#c0392b", lineHeight: 1 }}>
                        {data.signals.condition.pct_good_excellent}<span style={{ fontSize: 14, color: MI_MUTE }}>%</span>
                      </div>
                      <div style={{ fontSize: 10.5, color: MI_MUTE, marginTop: 6 }}>
                        ≥70% = healthy crop · 60-70% = avg · &lt;60% = stressed
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: MI_MUTE, fontStyle: "italic", marginTop: 8 }}>
                      First NASS condition score lands ~late June once corn is up.
                      <br /><br />
                      Will read as %Good+%Excellent. CME futures literally move every Monday afternoon when this number publishes.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Spring Conditions — WNY-aggregated rainfall + heat, side-by-side
                with the 14-day forecast. The single biggest sanity-check on the
                yield call: if it says "wet & getting wetter" alongside a slow
                pace, the forecast haircut is real. ── */}
          {data.wx_pulse && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef3ee", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontWeight: 800, color: "#1a2e1a" }}>📅 Forecast &amp; Outlook — New York</span>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {(() => {
                    if (!fcSyncedAt) return null;
                    const hrs = (Date.now() - new Date(fcSyncedAt).getTime()) / 3600000;
                    const lbl = hrs < 1 ? `${Math.max(1, Math.round(hrs * 60))}m ago` : hrs < 48 ? `${Math.round(hrs)}h ago` : `${Math.round(hrs / 24)}d ago`;
                    const stale = hrs > 6;   // forecast cron runs every 3h — >6h means a run was missed
                    return (
                      <span title={`Open-Meteo forecast last refreshed ${new Date(fcSyncedAt).toLocaleString()}. Auto-refreshes every 3 hours.`}
                        style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: stale ? "#fbe5e0" : "#e8f3e8", color: stale ? "#b04a2a" : "#2d6a2d", border: `1px solid ${stale ? "#e6c4b6" : "#cfe0cf"}` }}>
                        {stale ? "⚠ " : "🛰 "}forecast {lbl}
                      </span>
                    );
                  })()}
                  <span style={{ fontSize: 11, color: MI_MUTE }}>High-def 1,271-cell grid · season-to-date vs 1995–2025 normal · forecast = next ~16d</span>
                </span>
              </div>
              {/* Forecast outlook banner — how the 16-day forward weather moves the
                  early-season stress drag (the live yield signal). Driven by
                  miEarlyStress's forecast pass with lead-time confidence decay. */}
              {data.early_stress && data.early_stress.forecast_days > 0 && (() => {
                const es = data.early_stress;
                const o = es.forecast_outlook;
                const col = o === "improving" ? "#2d7a2d" : o === "worsening" ? "#b04a2a" : "#7a6a3a";
                const bg = o === "improving" ? "#eef7ee" : o === "worsening" ? "#fbeee8" : "#fbf8ec";
                const label = o === "improving" ? "Improving" : o === "worsening" ? "Worsening" : "Steady";
                const icon = o === "improving" ? "↗" : o === "worsening" ? "↘" : "→";
                const moved = (es.obs_drag_pct != null && es.drag_pct != null) ? (es.obs_drag_pct - es.drag_pct) : 0;
                return (
                  <div style={{ padding: "10px 16px", background: bg, borderBottom: "1px solid #eef3ee", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 800, color: col, fontSize: 13 }}>{icon} {es.forecast_days}-day outlook: {label}</span>
                    <span style={{ fontSize: 12, color: "#3a4a3a" }}>
                      {moved > 0.05
                        ? <>The forward weather <b style={{ color: "#2d7a2d" }}>eases</b> the early-stress drag <b>{es.obs_drag_pct}% → {es.drag_pct}%</b></>
                        : moved < -0.05
                        ? <>The forward weather <b style={{ color: "#b04a2a" }}>deepens</b> the early-stress drag <b>{es.obs_drag_pct}% → {es.drag_pct}%</b></>
                        : <>The forward weather holds the early-stress drag near <b>{es.drag_pct}%</b></>}
                      {" "}· lead-time-weighted ({es.forecast_recovery >= es.forecast_stress ? "warm/dry-leaning" : "cool/wet-leaning"})
                    </span>
                  </div>
                );
              })()}
              <div style={{ padding: "14px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                {/* Last-14d precip */}
                <div style={{ background: "#f4f8fb", border: "1px solid #cfdfe8", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: "#5a7388" }}>💧 Last 14d rain</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#1a4a7a", lineHeight: 1.1 }}>{data.wx_pulse.last14_precip_in}<span style={{ fontSize: 13, color: "#7a9bb0" }}>"</span></div>
                  <div style={{ fontSize: 11, color: "#5a7388" }}>WNY avg</div>
                </div>
                {/* Next 14d forecast precip */}
                <div style={{ background: "#fdf6e7", border: "1px solid #e6d3a3", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: "#7a5b00" }}>☔ Next 14d (forecast)</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#b88500", lineHeight: 1.1 }}>{data.wx_pulse.next14_precip_in}<span style={{ fontSize: 13, color: "#c2a050" }}>"</span></div>
                  <div style={{ fontSize: 11, color: "#7a5b00" }}>{data.wx_pulse.next14_workable}/14 workable days</div>
                </div>
                {/* Season precip vs normal */}
                <div style={{ background: "#f7faf7", border: "1px solid #cfe0cf", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: "#5a7a5a" }}>Season precip vs normal</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#1a4a1a", lineHeight: 1.1 }}>{data.wx_pulse.season_precip_in}<span style={{ fontSize: 13, color: "#6a8a6a" }}>"</span></div>
                  <div style={{ fontSize: 11, color: data.wx_pulse.season_precip_anom != null && data.wx_pulse.season_precip_anom > 0.15 ? "#c0392b" : data.wx_pulse.season_precip_anom < -0.15 ? "#b85000" : "#5a7a5a", fontWeight: 600 }}>
                    {data.wx_pulse.season_precip_anom != null ? `${data.wx_pulse.season_precip_anom >= 0 ? "▲" : "▼"} ${Math.round(Math.abs(data.wx_pulse.season_precip_anom) * 100)}%` : "—"} vs {data.wx_pulse.season_precip_normal_in}" normal
                  </div>
                </div>
                {/* Season GDD vs normal */}
                <div style={{ background: "#fbf3f0", border: "1px solid #e8c9bb", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: "#7a4a3a" }}>🔥 Season GDD vs normal</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#8c3a14", lineHeight: 1.1 }}>{data.wx_pulse.season_gdd}</div>
                  <div style={{ fontSize: 11, color: data.wx_pulse.season_gdd_anom != null && Math.abs(data.wx_pulse.season_gdd_anom) > 0.15 ? "#c0392b" : "#7a4a3a", fontWeight: 600 }}>
                    {data.wx_pulse.season_gdd_anom != null ? `${data.wx_pulse.season_gdd_anom >= 0 ? "▲" : "▼"} ${Math.round(Math.abs(data.wx_pulse.season_gdd_anom) * 100)}%` : "—"} vs {data.wx_pulse.season_gdd_normal} normal
                  </div>
                </div>
              </div>
              {/* 14-day forecast strip — bar per day, heavy-rain days flagged */}
              {data.wx_pulse.next14_daily && data.wx_pulse.next14_daily.length > 0 && (() => {
                const days = data.wx_pulse.next14_daily;
                const maxP = Math.max(0.5, ...days.map((d) => d.p || 0));
                const today = data.wx_pulse.asof;
                return (
                  <div style={{ padding: "4px 16px 16px" }}>
                    <div style={{ fontSize: 11, color: MI_MUTE, marginBottom: 6 }}>14-day rainfall + temperature forecast (WNY avg) — bars ≥ 0.3" flag as not-field-workable · high/low °F below each day</div>
                    {/* ⚠ FOUR DAYS OF THIS FORECAST WERE UNREACHABLE ON A PHONE.
                        `1fr` does not mean "shrink to fit" — a grid item's default
                        `min-width: auto` stops it going below its own content, and
                        each column carries a date and a high/low. Measured on the
                        shipped build at a 390px viewport: 14 columns needing 26px
                        each is 410px of content in a 288px box, clipped by an
                        ancestor with `overflow-x: hidden`, so the last days simply
                        were not there — no scrollbar, no hint, nothing to say the
                        14-day button had shown fewer than 14 days.
                        SCROLL RATHER THAN WRAP here, unlike every tab strip in this
                        app: these are BARS on a shared baseline (`align-items: end`
                        over a fixed 104px), and a bar chart broken across two rows
                        no longer compares. `minmax` keeps a column readable instead
                        of letting the labels collide. */}
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${days.length}, minmax(26px, 1fr))`,
                                  gap: 3, alignItems: "end", height: 104, overflowX: "auto" }}>
                      {days.map((d, i) => {
                        const h = d.p == null ? 0 : (d.p / maxP) * 70;
                        const block = d.p != null && d.p >= 0.3;
                        const isToday = d.d === today;
                        // Heat flag: ≥90°F high is the grain-fill stress threshold the model uses.
                        const hot = d.tx != null && d.tx >= 90;
                        const tTip = d.tx != null ? ` · ${d.tx}°/${d.tn != null ? d.tn + "°" : "—"} F` : "";
                        return (
                          <div key={i} title={`${d.d}: ${d.p != null ? d.p.toFixed(2) + '"' : "no data"}${tTip}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: 104 }}>
                            <div style={{ fontSize: 10, color: block ? "#c0392b" : "#7a9bb0", fontWeight: 700, marginBottom: 2 }}>{d.p != null ? d.p.toFixed(2) : ""}</div>
                            <div style={{ width: "75%", height: Math.max(2, h), background: block ? "#d97264" : "#4a8eb8", borderRadius: 2, opacity: d.p == null ? 0.15 : 1 }} />
                            <div style={{ fontSize: 10, color: isToday ? "#1a4a1a" : "#9ab", fontWeight: isToday ? 800 : 500, marginTop: 3 }}>{d.d.slice(5).replace("-", "/")}</div>
                            <div style={{ fontSize: 9.5, marginTop: 2, whiteSpace: "nowrap", fontWeight: 700 }}>
                              <span style={{ color: hot ? "#c0392b" : "#b8722a" }}>{d.tx != null ? d.tx : "—"}</span>
                              <span style={{ color: "#c8d2c8" }}>/</span>
                              <span style={{ color: "#4a8eb8", fontWeight: 600 }}>{d.tn != null ? d.tn : "—"}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Cumulative GDD & Rainfall — collapsible, interactive (tap a
              point for values). Current-year line is solid for observed, dashed
              for the 14-day forecast tail, with a "today" marker. Lives on the
              main page (was previously orphaned inside the gated-off Model Lab). ── */}
          {data.wx_chart && (() => {
            const cur = data.wx_chart.cur || [], normalFull = data.wx_chart.normal || [];
            const fcFrom = (() => { const i = cur.findIndex((c) => c.fc); return i >= 0 ? i : null; })();
            // Trim the normal line to the SAME window as the 2026 line (through
            // the forecast horizon) so the two are compared like-for-like at every
            // x. Leaving normal running to late July while 2026 stops at the
            // forecast made 2026 look like it fell below normal when it hadn't.
            const xL = cur.map((o) => o.label);
            const normal = normalFull.slice(0, cur.length);
            return (
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "12px 16px", borderBottom: wxOpen ? "1px solid #eef3ee" : "none" }}>
                  <span onClick={() => setWxOpen((v) => !v)} style={{ fontWeight: 800, color: "#1a2e1a", cursor: "pointer", flex: 1 }}>{wxOpen ? "▾" : "▸"} 📈 Cumulative GDD &amp; Rainfall <span style={{ fontWeight: 600, color: "#9ab", fontSize: 11 }}>· {year} to date vs 1995–2025 normal{fcFrom != null ? " · incl. 14-day forecast" : ""}</span></span>
                  {wxOpen ? (
                    <button onClick={(e) => { e.stopPropagation(); setWxBig((v) => !v); }}
                      style={{ fontSize: 11, fontWeight: 700, color: wxBig ? "#fff" : "#3a5a3a", background: wxBig ? MI_GREEN : "#eef3ee", border: "1px solid " + (wxBig ? MI_GREEN : "#d6e0d6"), borderRadius: 6, padding: "4px 10px", cursor: "pointer", whiteSpace: "nowrap" }}>
                      {wxBig ? "⤡ Shrink" : "⤢ Enlarge"}
                    </button>
                  ) : <span style={{ fontSize: 11, color: MI_MUTE }} onClick={() => setWxOpen((v) => !v)}>tap to view</span>}
                </div>
                {wxOpen && (
                  <div style={{ display: "flex", gap: 18, flexWrap: "wrap", flexDirection: wxBig ? "column" : "row", padding: 14 }}>
                    <MiChart
                      title="Growing degree days — cumulative"
                      sub={`WNY avg · ${year} to date vs same-window normal`}
                      xLabels={xL}
                      fcFrom={fcFrom}
                      big={wxBig}
                      fmtVal={(v) => Math.round(v).toLocaleString()}
                      series={[
                        { name: `${year}`, color: MI_GREEN, w: 2.5, vals: cur.map((c) => c.gdd) },
                        { name: "1995–2025 normal", color: "#94a3b8", dash: "5,4", vals: normal.map((n) => n.gdd) },
                      ]}
                    />
                    <MiChart
                      title={'Rainfall — cumulative (")'}
                      sub={`WNY avg · ${year} to date vs same-window normal`}
                      xLabels={xL}
                      fcFrom={fcFrom}
                      big={wxBig}
                      fmtVal={(v) => `${v.toFixed(1)}"`}
                      series={[
                        { name: `${year}`, color: "#2563eb", w: 2.5, vals: cur.map((c) => c.pr) },
                        { name: "1995–2025 normal", color: "#94a3b8", dash: "5,4", vals: normal.map((n) => n.pr) },
                      ]}
                    />
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Analog Years ──────────────────────────────────────────────
              For each historical year, compute its spring-to-date weather
              signature (precip + GDD), find the closest matches to 2026 by
              Euclidean distance, then pair each with its actual final NASS
              yield. The implied yield is inverse-distance-weighted across
              the matches. This is the most credible piece of the page for
              an outside audience because it's not a model output — it's
              "history says when this pattern showed up before, here's what
              happened." ── */}
          {false && data.analog_years && data.analog_years.analogs && data.analog_years.analogs.length > 0 && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef3ee", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontWeight: 800, color: "#1a2e1a" }}>📚 Analog Years — when did this pattern show up before?</span>
                <span style={{ fontSize: 11, color: MI_MUTE }}>Closest historical matches by spring-to-date precip + GDD signature, paired with that year's actual final NASS yield.</span>
              </div>
              {data.analog_years.implied_yield != null && (
                <div style={{ padding: "14px 16px", background: "linear-gradient(90deg, #f0f6f0 0%, #e7f0e7 100%)", borderBottom: "1px solid #dfe7df", display: "flex", flexWrap: "wrap", gap: "12px 28px", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: "#5a7a5a", fontWeight: 700 }}>Analog-implied yield</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: MI_GREEN, lineHeight: 1 }}>{data.analog_years.implied_yield}<span style={{ fontSize: 13, color: "#7a9b7a", fontWeight: 600 }}> bu/ac</span></div>
                  </div>
                  {data.analog_years.implied_range && (
                    <div>
                      <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: "#5a7a5a", fontWeight: 700 }}>Range of {data.analog_years.n_with_yield} analogs</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#1a4a1a" }}>{data.analog_years.implied_range.lo} – {data.analog_years.implied_range.hi} bu/ac</div>
                    </div>
                  )}
                  <div style={{ flex: 1 }} />
                  <div style={{ fontSize: 11, color: MI_MUTE, maxWidth: 340, textAlign: "right" }}>
                    Weighted by inverse distance — closer match = bigger weight. Independent of our recent-avg × weather × pace model.
                  </div>
                </div>
              )}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f7faf7" }}>
                      {["Year", "Spring precip", "vs 2026", "Spring GDD", "vs 2026", "Days obs", "Final yield (NASS)", "Distance"].map((h, i) => (
                        <th key={i} style={{ padding: "8px 12px", textAlign: i === 0 ? "left" : "right", color: MI_MUTE, fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase", borderBottom: "2px solid #dde7dd", whiteSpace: "nowrap", fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.analog_years.analogs.map((a, i) => {
                      const fmtPct = (v) => v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`;
                      const pColor = Math.abs(a.precip_diff_pct) <= 15 ? "#3f7d3f" : Math.abs(a.precip_diff_pct) <= 35 ? "#b08900" : "#b85000";
                      const gColor = Math.abs(a.gdd_diff_pct) <= 10 ? "#3f7d3f" : Math.abs(a.gdd_diff_pct) <= 20 ? "#b08900" : "#b85000";
                      const rank = i + 1;
                      return (
                        <tr key={a.year} style={{ borderBottom: "1px solid #f0f4f0", background: i === 0 ? "#fafffa" : undefined }}>
                          <td style={{ padding: "9px 12px", fontWeight: 800, color: "#1a2e1a" }}>
                            <span style={{ display: "inline-block", width: 18, height: 18, borderRadius: 4, background: i === 0 ? MI_GREEN : "#cfdfcf", color: i === 0 ? "#fff" : "#3a5a3a", textAlign: "center", fontSize: 10.5, lineHeight: "18px", fontWeight: 800, marginRight: 8 }}>{rank}</span>
                            {a.year}
                          </td>
                          <td style={{ padding: "9px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{a.precip_in}"</td>
                          <td style={{ padding: "9px 12px", textAlign: "right", color: pColor, fontWeight: 700 }}>{fmtPct(a.precip_diff_pct)}</td>
                          <td style={{ padding: "9px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{a.gdd.toLocaleString()}</td>
                          <td style={{ padding: "9px 12px", textAlign: "right", color: gColor, fontWeight: 700 }}>{fmtPct(a.gdd_diff_pct)}</td>
                          <td style={{ padding: "9px 12px", textAlign: "right", color: MI_MUTE }}>{a.days}d</td>
                          <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800, color: a.final_yield != null ? "#1a2e1a" : MI_MUTE }}>{a.final_yield != null ? `${a.final_yield} bu/ac` : "—"}</td>
                          <td style={{ padding: "9px 12px", textAlign: "right", color: MI_MUTE, fontVariantNumeric: "tabular-nums" }}>{a.distance}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: "10px 16px", fontSize: 11, color: MI_MUTE, background: "#f7faf7" }}>
                2026 to-date: <b>{data.analog_years.cur.precip_in}"</b> precip · <b>{data.analog_years.cur.gdd.toLocaleString()}</b> GDD over {data.analog_years.cur.days}d (through {data.analog_years.cur.asof}). Distance is normalized — 0.10 = ~10% off across the (precip, GDD) plane.
              </div>
            </div>
          )}

          {/* ── Cohort weather model ─────────────────────────────────────────
              The point Kevin made: yield isn't season-average weather, it's
              about WHEN acres get planted (their silking window) × what
              weather happens DURING that window. A May-15-planted cohort
              silks ~mid-July; a June-10-planted cohort silks ~early August.
              Late-planted = exposed to August drought; early-planted is fine.
              This card shows the book broken into weekly planting cohorts,
              each with its own silking-period weather signal. ── */}
          {false && data.cohort_summary && data.cohort_summary.weekly && data.cohort_summary.weekly.length > 0 && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef3ee", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontWeight: 800, color: "#1a2e1a" }}>🌽 Planting Cohorts → Silking Weather</span>
                <span style={{ fontSize: 11, color: MI_MUTE }}>Each bucket = a week of planted (or projected) acres. Silking ≈ +60–75d; grain fill ≈ +75–105d. Weather DURING that window sets the yield ceiling.</span>
              </div>
              <div style={{ padding: "12px 16px", display: "flex", flexWrap: "wrap", gap: "8px 22px", fontSize: 12, color: MI_GREEN2, borderBottom: "1px solid #eef3ee" }}>
                <span><b>{data.cohort_summary.planted_ac.toLocaleString()}</b> ac planted</span>
                <span><b>{data.cohort_summary.projected_ac.toLocaleString()}</b> ac projected catch-up</span>
                <span><b>{data.cohort_summary.n_cohorts}</b> cohorts</span>
                <span>Book weather factor: <b style={{ color: miDeltaColor(data.cohort_summary.book_factor - 1) }}>×{data.cohort_summary.book_factor}</b></span>
              </div>
              {/* Weekly cohort grid */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f7faf7" }}>
                      {["Plant week", "Acres", "Status", "Silks ~", "Silk precip", "Silk GDD", "VPD stress", "Silk evidence", "Wx factor"].map((h, i) => (
                        <th key={i} style={{ padding: "8px 10px", textAlign: i <= 2 ? "left" : "right", color: MI_MUTE, fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase", borderBottom: "2px solid #dde7dd", whiteSpace: "nowrap", fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.cohort_summary.weekly.map((w, i) => {
                      const pdt = new Date(w.week_start + "T00:00:00");
                      const silkD = new Date(pdt); silkD.setDate(silkD.getDate() + 67);
                      const silkLabel = `${silkD.getMonth() + 1}/${silkD.getDate()}`;
                      const isProj = w.projected_acres > w.planted_acres;
                      const wkLabel = `${pdt.getMonth() + 1}/${pdt.getDate()}`;
                      const evidPct = Math.round(w.silk_evidence * 100);
                      const pAnomColor = w.silk_precip_anom < -0.15 ? "#b85000" : w.silk_precip_anom > 0.35 ? "#c0392b" : "#3f7d3f";
                      const fmtAnom = (v) => `${v >= 0 ? "+" : ""}${Math.round(v * 100)}%`;
                      const vpdDays = w.silk_vpd_stress_days || 0;
                      const vpdColor = vpdDays >= 5 ? "#c0392b" : vpdDays >= 2 ? "#b08900" : MI_MUTE;
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #f0f4f0" }}>
                          <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1a2e1a" }}>Wk of {wkLabel}</td>
                          <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{w.acres.toLocaleString()}</td>
                          <td style={{ padding: "8px 10px", color: isProj ? "#b08900" : MI_GREEN, fontWeight: 700, fontSize: 11 }}>{isProj ? "PROJECTED" : "PLANTED"}</td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: MI_GREEN2 }}>{silkLabel}</td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: pAnomColor, fontWeight: 600 }}>{fmtAnom(w.silk_precip_anom)}</td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: miDeltaColor(w.silk_gdd_anom) }}>{fmtAnom(w.silk_gdd_anom)}</td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: vpdColor, fontWeight: vpdDays > 0 ? 700 : 400 }} title={`Days with VPD > 2.0 kPa during the 15-day silking window. Peak VPD seen: ${w.silk_vpd_max_kpa || 0} kPa.`}>
                            {vpdDays > 0 ? `${vpdDays}d` : "—"}
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: evidPct >= 50 ? MI_GREEN : evidPct >= 15 ? "#b08900" : MI_MUTE }}>{evidPct}%</td>
                          <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 800, color: miDeltaColor(w.factor - 1) }}>×{w.factor}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: "10px 16px", fontSize: 11, color: MI_MUTE, background: "#f7faf7" }}>
                <b>How to read this:</b> Silk evidence rises as the calendar approaches each cohort's silking window — today all silking is &gt; 50 days out, so evidence is low and the factor hovers near ×1.00 (climatology fallback). As July rolls in, silking for early cohorts becomes observed and the factor sharpens to whatever the actual weather delivered. A wet spring → dry August would show as: early cohorts (silking July) ≈ ×1.0, late cohorts (silking August) ≈ ×0.85. <b>VPD stress</b> = days during the silking window with vapor pressure deficit &gt; 2.0 kPa (silks shrivel, pollen dies). Each VPD-stress day takes ~0.5% off yield, capped at 8% total.
              </div>
            </div>
          )}

          {/* ── Phase 2 SHADOW: per-cell gridMET weather factor vs incumbent ──
                Same cohort weather model, sourced from the 1,271-cell gridMET
                grid + per-cell 1991-2020 normals instead of 16 county-centroid
                POWER points. DISPLAY ONLY — the live forecast above is
                unchanged. Promotion waits on the historical backtest gate. ── */}
          {false && gridShadow && gridShadow.counties && Object.keys(gridShadow.counties).length > 0 && (() => {
            const byFips = {};
            (data.counties || []).forEach((c) => { byFips[c.fips] = c; });
            // The LIVE forecast (c.forecast_yield) is now grid-powered. This card
            // shows what changed: reconstruct the OLD county-centroid (POWER)
            // forecast from the live one by undoing the grid/POWER factor ratio.
            // newY = live (grid); oldY = newY × (powerWx/gridWx) × (powerES/gridES).
            const rowsSh = MI_MODEL_COUNTIES.map((m) => {
              const c = byFips[m.fips], g = gridShadow.counties[m.fips];
              if (!c || !g) return null;
              const iF = (c.weather && c.weather.factor) || null;            // OLD POWER cohort factor
              const iES = (c.early_stress && c.early_stress.factor) || null; // OLD POWER early-stress
              const newY = c.forecast_yield != null ? c.forecast_yield : null;
              const wRatio = (g.factor && g.factor > 0 && iF != null) ? iF / g.factor : 1;
              const esRatio = (g.early_stress_factor && g.early_stress_factor > 0 && iES != null) ? iES / g.early_stress_factor : 1;
              const oldY = (newY != null) ? newY * wRatio * esRatio : null;
              return { ...m, g, i: iF, iES, newY, oldY, dY: (newY != null && oldY != null) ? newY - oldY : null, src: c.wx_source, acres: c.pred_acres || 0 };
            }).filter(Boolean).sort((a, b) => (a.g.precip_anom ?? 0) - (b.g.precip_anom ?? 0));
            const incVals = rowsSh.map((r) => r.i).filter((v) => v != null);
            const incWny = incVals.length ? incVals.reduce((a, b) => a + b, 0) / incVals.length : null;
            const mAvg = (arr) => { const v = arr.filter((x) => x != null); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };
            const incESWny = mAvg(rowsSh.map((r) => r.iES));
            const gridESWny = mAvg(rowsSh.map((r) => r.g.early_stress_factor));
            // WNY rollup — SIMPLE MEAN across counties (matches the page hero).
            // acre-weighted to match the page hero (total bu / total acres)
            const acc = rowsSh.filter((r) => r.newY != null && r.oldY != null)
              .reduce((o, r) => { const w = r.acres > 0 ? r.acres : 0; o.lw += r.oldY * w; o.nw += r.newY * w; o.w += w; o.ls += r.oldY; o.ns += r.newY; o.n++; return o; }, { lw: 0, nw: 0, w: 0, ls: 0, ns: 0, n: 0 });
            const oldWnyY = acc.w > 0 ? acc.lw / acc.w : (acc.n > 0 ? acc.ls / acc.n : null);
            const newWnyY = acc.w > 0 ? acc.nw / acc.w : (acc.n > 0 ? acc.ns / acc.n : null);
            const dWnyY = (oldWnyY != null && newWnyY != null) ? newWnyY - oldWnyY : null;
            const nCornSoil = rowsSh.filter((r) => r.src === "grid_cornsoil").length;
            const fmtPct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Math.round(v * 100)}%`);
            const fmtDY = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${miR1(v)}`);
            return (
              <div style={{ ...card, padding: 0, overflow: "hidden", border: "1px solid #cfe0cf" }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef3ee", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <span style={{ fontWeight: 800, color: "#1a2e1a" }}>🛰️ High-Def Model — gridMET 4km × CDL corn × SSURGO soil</span>
                  <span style={{ fontSize: 10.5, color: "#fff", background: MI_GREEN, padding: "2px 8px", borderRadius: 10, fontWeight: 700, letterSpacing: 0.3 }}>● NOW LIVE</span>
                </div>
                {/* Headline: old county-centroid model → new high-def (now live) */}
                <div style={{ padding: "14px 16px", borderBottom: "1px solid #eef3ee", display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-end" }}>
                  <div>
                    <div style={{ fontSize: 10.5, color: MI_MUTE, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>Old county-centroid</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: MI_MUTE, lineHeight: 1.1 }}>{oldWnyY != null ? miR1(oldWnyY) : "—"}<span style={{ fontSize: 12, fontWeight: 600 }}> bu/ac</span></div>
                  </div>
                  <div style={{ fontSize: 22, color: MI_MUTE, paddingBottom: 4 }}>→</div>
                  <div>
                    <div style={{ fontSize: 10.5, color: MI_GREEN, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>High-def (LIVE)</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: MI_GREEN, lineHeight: 1.1 }}>{newWnyY != null ? miR1(newWnyY) : "—"}<span style={{ fontSize: 13, color: MI_MUTE, fontWeight: 600 }}> bu/ac</span></div>
                  </div>
                  <div style={{ paddingBottom: 4 }}>
                    <div style={{ fontSize: 10.5, color: MI_MUTE, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>Δ from old</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: dWnyY == null || Math.abs(dWnyY) < 0.05 ? MI_MUTE : miDeltaColor(dWnyY) }}>{fmtDY(dWnyY)}<span style={{ fontSize: 12, fontWeight: 600 }}> bu/ac</span></div>
                  </div>
                  <div style={{ marginLeft: "auto", fontSize: 11.5, color: MI_MUTE, paddingBottom: 4, textAlign: "right" }}>
                    Silking ×{incWny != null ? miR3(incWny) : "—"}→×{gridShadow.wny_factor} <span style={{ fontStyle: "italic" }}>(dormant until July)</span><br />
                    Early-stress ×{incESWny != null ? miR3(incESWny) : "—"}→×{gridESWny != null ? miR3(gridESWny) : "—"} · <b>{nCornSoil}/{rowsSh.length}</b> counties corn×soil weighted · through {gridShadow.asof}
                  </div>
                </div>
                <div style={{ padding: "10px 16px", fontSize: 11.5, color: MI_MUTE, borderBottom: "1px solid #eef3ee" }}>
                  <b>The live forecast above is now this model.</b> The two weather terms (silking/grain-fill cohort + early-season stress) are re-derived from {gridShadow.cells_used} gridMET cells (~4&nbsp;km) vs each cell's <i>1991-2020 normal</i>, then aggregated to county weighted by <b>where the bushels come from</b> (CDL corn × SSURGO soil) instead of one county-centroid point. Baseline, pace and drought are unchanged from the old model, so the Δ is purely the finer weather × production-weighting. Silking is still ≈×1.00 (&gt;50 days out); today's Δ is early-season stress. Counties whose CDL isn't fully loaded yet fall back to uniform grid weighting until the overnight backfill completes.
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#f7faf7" }}>
                        {["County", "Old yield", "New (LIVE)", "Δ bu/ac", "Weighting", "Precip vs nrml", "GDD vs nrml"].map((h, i) => (
                          <th key={i} style={{ padding: "8px 10px", textAlign: i === 0 ? "left" : "right", color: MI_MUTE, fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase", borderBottom: "2px solid #dde7dd", whiteSpace: "nowrap", fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rowsSh.map((r, i) => {
                        const pColor = (r.g.precip_anom ?? 0) < -0.15 ? "#b85000" : (r.g.precip_anom ?? 0) > 0.35 ? "#c0392b" : "#3f7d3f";
                        const srcLabel = r.src === "grid_cornsoil" ? "corn×soil" : r.src === "grid_uniform" ? "grid (uniform)" : "POWER";
                        const srcColor = r.src === "grid_cornsoil" ? MI_GREEN : r.src === "grid_uniform" ? "#b08900" : MI_MUTE;
                        return (
                          <tr key={i} style={{ borderBottom: "1px solid #f0f4f0" }}>
                            <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1a2e1a", textTransform: "capitalize" }} title={`${r.g.cell_n} cells · mean soil NCCPI ${r.g.mean_nccpi ?? "—"} · cohort ×${r.g.factor} (old ×${r.i != null ? miR3(r.i) : "—"}) · early-stress ×${r.g.early_stress_factor} (old ×${r.iES != null ? miR3(r.iES) : "—"})`}>{r.name.toLowerCase()}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", color: MI_MUTE }}>{r.oldY != null ? miR1(r.oldY) : "—"}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 800, color: MI_GREEN }}>{r.newY != null ? miR1(r.newY) : "—"}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: r.dY == null || Math.abs(r.dY) < 0.05 ? MI_MUTE : miDeltaColor(r.dY) }}>{fmtDY(r.dY)}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: srcColor, fontSize: 11 }}>{srcLabel}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", color: pColor, fontWeight: 600 }} title={`Season-to-date ${r.g.season_precip_in}″ vs ${r.g.normal_precip_in}″ normal`}>{fmtPct(r.g.precip_anom)}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", color: miDeltaColor(r.g.gdd_anom) }} title={`Season-to-date ${r.g.season_gdd} GDD vs ${r.g.normal_gdd} normal`}>{fmtPct(r.g.gdd_anom)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: "10px 16px", fontSize: 11, color: MI_MUTE, background: "#f7faf7" }}>
                  <b>Switched live 2026-05-30.</b> gridMET uses a proper 30-yr (1991-2020) normal vs the old 16-point POWER baseline, and weights weather by corn×soil production location. Honest caveats still open: (1) full out-of-sample backtest needs per-cell gridMET <i>history</i> (not yet ingested — grid daily starts Apr 2026); the switch rests on more-defensible inputs + method, validated season-long against the old model here. (2) CDL corn is still completing for the northern counties (uniform fallback until then). Sorted driest→wettest.
                </div>
              </div>
            );
          })()}

          {/* ── Interactive WNY county map — pick a metric to color by ── */}
          {data.counties && data.counties.length > 0 && (
            <MIYieldMap counties={data.counties} gridShadow={mapGridShadow} isMobile={isMobile} catchment={catchment} setCatchment={setCatchment} data={data} region={region} setRegion={setRegion} regions={MI_REGIONS} onNeedCellLayers={ensureCellLayers} cellLayersState={gridShadow ? "ready" : mapLayersState} isPortal={isPortal} />
          )}

          {/* ── WNY Grain Production: this year vs last 5 ───────────────────
                NASS-actual production per year (acres × yield, per county,
                summed across the 16 WNY counties; missing county-years
                imputed with that county's own historical mean). The current-
                year prediction bar uses the model's pred_bu. ──────────── */}
          {false && data.historical_production && data.historical_production.length > 0 && data.production?.pred_bu != null && (() => {
            const hist = data.historical_production;
            const histVals = hist.map((h) => h.total_mbu);
            const histAvg = histVals.reduce((a, b) => a + b, 0) / histVals.length;
            const predMbu = Math.round(data.production.pred_bu / 1e5) / 10;
            const allVals = [...histVals, predMbu, histAvg];
            const yMax = Math.max(...allVals) * 1.12;
            const yMin = 0;
            const yRange = yMax - yMin;
            const svgW = 720, svgH = 280, padL = 50, padR = 20, padT = 30, padB = 50;
            const chartW = svgW - padL - padR;
            const chartH = svgH - padT - padB;
            const bars = [...hist.map((h) => ({ label: String(h.year), value: h.total_mbu, isFc: false, imp: h.imputed_counties, obs: h.observed_counties })),
                          { label: `${year}f`, value: predMbu, isFc: true, imp: 0, obs: 0 }];
            const barW = (chartW / bars.length) * 0.65;
            const slot = chartW / bars.length;
            const yPos = (v) => padT + chartH - ((v - yMin) / yRange) * chartH;
            const histAvgY = yPos(histAvg);
            const yTicks = [];
            for (let i = 0; i <= 4; i++) {
              const v = yMin + (yRange * i / 4);
              yTicks.push({ v: Math.round(v * 10) / 10, y: yPos(v) });
            }
            const predVsAvgPct = histAvg > 0 ? Math.round((predMbu / histAvg - 1) * 100) : 0;
            return (
              <div style={{ ...card, padding: 18 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 8 }}>
                  <span style={{ fontWeight: 800, color: MI_GREEN, fontSize: 16 }}>📊 WNY Corn Grain Production — {year} forecast vs last {hist.length} years</span>
                  <span style={{ fontSize: 12, color: predVsAvgPct >= 0 ? "#2d6a1a" : "#c0392b", fontWeight: 700 }}>
                    {predMbu}M bu {predVsAvgPct >= 0 ? "+" : ""}{predVsAvgPct}% vs 5-yr avg ({histAvg.toFixed(1)}M bu)
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: MI_MUTE, marginBottom: 10 }}>
                  Historical = NASS ACRES_HARVESTED × YIELD, summed across the 16 WNY counties. County-years suppressed by NASS for privacy are imputed with that county's own mean (counts shown in tooltip).
                </div>
                <div style={{ overflowX: "auto" }}>
                  <svg viewBox={`0 0 ${svgW} ${svgH}`} style={{ width: "100%", height: "auto", display: "block" }}>
                    {/* y-axis grid + labels */}
                    {yTicks.map((t, i) => (
                      <g key={i}>
                        <line x1={padL} x2={svgW - padR} y1={t.y} y2={t.y} stroke="#eef3ee" strokeWidth="1" />
                        <text x={padL - 8} y={t.y + 4} fontSize="11" fill={MI_MUTE} textAnchor="end">{t.v}M</text>
                      </g>
                    ))}
                    {/* 5-yr avg reference line */}
                    <line x1={padL} x2={svgW - padR} y1={histAvgY} y2={histAvgY} stroke="#94a3b8" strokeDasharray="4,4" strokeWidth="1.5" />
                    <text x={svgW - padR - 4} y={histAvgY - 6} fontSize="10.5" fill="#6a8a6a" textAnchor="end" fontWeight="700">5-yr avg {histAvg.toFixed(1)}M</text>
                    {/* bars */}
                    {bars.map((b, i) => {
                      const xCenter = padL + slot * (i + 0.5);
                      const x = xCenter - barW / 2;
                      const y = yPos(b.value);
                      const h = (padT + chartH) - y;
                      const fill = b.isFc ? MI_GREEN : "#a8c8a8";
                      const stroke = b.isFc ? MI_GREEN2 : "none";
                      const labelColor = b.isFc ? MI_GREEN : "#5a7a5a";
                      const titleParts = [`${b.label}: ${b.value}M bu`];
                      if (!b.isFc) {
                        titleParts.push(`${b.obs} of ${MI_MODEL_COUNTIES.length} counties observed`);
                        if (b.imp > 0) titleParts.push(`${b.imp} imputed`);
                      }
                      return (
                        <g key={i}>
                          <rect x={x} y={y} width={barW} height={Math.max(2, h)} fill={fill} stroke={stroke} strokeWidth={b.isFc ? 2 : 0} rx="2">
                            <title>{titleParts.join(" — ")}</title>
                          </rect>
                          <text x={xCenter} y={y - 6} fontSize="12" fill={labelColor} textAnchor="middle" fontWeight={b.isFc ? 800 : 700}>{b.value}</text>
                          <text x={xCenter} y={padT + chartH + 18} fontSize="12" fill={b.isFc ? MI_GREEN : MI_MUTE} textAnchor="middle" fontWeight={b.isFc ? 800 : 600}>{b.label}</text>
                          {!b.isFc && b.imp > 0 && (
                            <text x={xCenter} y={padT + chartH + 34} fontSize="9" fill="#b08900" textAnchor="middle" fontStyle="italic">
                              {b.imp} imputed
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </svg>
                </div>
              </div>
            );
          })()}

          {/* The banner that used to sit here is gone. It warned that a wet county
              might be reading high — but that premise did not survive checking:
              across 154 WNY county-years precip anomaly correlates +0.535 with
              yield, monotonically (very dry 135.8 bu → very wet 174.9), and it
              does not cost acres either (corr with abandonment −0.07; abandonment
              is worst in DROUGHT). In this region wet years ARE the good years,
              which is also why a waterlogging penalty has failed to fit three
              times — there is no penalty in the record to fit.
              What survives is narrow and belongs on the row, not in a banner: the
              factor is at its ceiling. See the ⚠ chip. */}

          {/* ── Counties (collapsible, sortable table; click a row for detail) ── */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div onClick={() => setShowCounties((s) => !s)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer", borderBottom: showCounties ? "1px solid #eef3ee" : "none" }}>
              <span style={{ fontWeight: 800, color: "#1a2e1a" }}>{showCounties ? "▾" : "▸"} Counties ({rows.length})</span>
              <span style={{ fontSize: 12, color: MI_MUTE }}>sorted by {SORT_LABEL[sortKey] || sortKey} · Wx×&gt;1 = better than normal · click a row</span>
            </div>
            {showCounties && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  {/* Header: click any column to sort by it. Sortable accessors include
                       nested per-county weather/drought/stress fields. */}
                  <thead><tr>
                    {[
                      ["name", "County"],
                      ["forecast_yield", "Yield"],
                      ["pred_acres", "Acres"],
                      ["pred_prod", "Prod"],
                      ["season_precip_in", "Rain (in)"],
                      ["precip_anom", "Rain anom"],
                      ["season_gdd", "GDD"],
                      ["gdd_anom", "GDD anom"],
                      ["drought_dsci", "DSCI"],
                      ["early_stress_drag", "Stress"],
                      ["pace_factor", "Pace×"],
                      ["total_wx_factor", "Wx×"],
                      ["confidence", "Conf"],
                    ].map(([k, lbl]) => (
                      <th key={k} onClick={() => setSortKey(k)} title={`Sort by ${lbl.toLowerCase()}`} style={{ textAlign: k === "name" ? "left" : "right", padding: "9px 8px", fontSize: 10.5, textTransform: "uppercase", color: sortKey === k ? "#1a2e1a" : MI_MUTE, borderBottom: "2px solid #dde7dd", cursor: "pointer", whiteSpace: "nowrap", fontWeight: sortKey === k ? 800 : 700, background: sortKey === k ? "#e8f0e8" : "transparent" }}>{lbl}{sortKey === k ? " ▾" : ""}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {rows.map((c) => (
                      <React.Fragment key={c.fips}>
                        <tr onClick={() => setExpanded((e) => (e === c.fips ? null : c.fips))} style={{ cursor: "pointer", background: expanded === c.fips ? "#f4f8f4" : "transparent" }}>
                          <td style={{ padding: "7px 10px", fontWeight: 700, borderBottom: "1px solid #f0f4f0", color: "#1a2e1a" }}>{expanded === c.fips ? "▾ " : "▸ "}{c.name}
                            {c.wet_z != null && c.wet_z >= 2.0 && (
                              <span title={miWetNote(c)} style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#1d5b7a", background: "#eaf4fa", border: "1px solid #b8d9e8", borderRadius: 4, padding: "1px 4px", letterSpacing: 0.3, whiteSpace: "nowrap" }}>
                                💧 {c.wet_z.toFixed(1)}σ RAIN
                              </span>
                            )}
                            {c.overreach && (
                              <span title={miOverreachNote(c)} style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#8a6d1f", background: "#fff6e6", border: "1px solid #e8d39a", borderRadius: 4, padding: "1px 4px", letterSpacing: 0.3, whiteSpace: "nowrap" }}>
                                ⚠ {c.overreach.pegged ? "CAPPED" : "OVER RECORD"}
                              </span>
                            )}</td>
                          <td style={{ ...tdR, fontWeight: 800, color: MI_GREEN, padding: "7px 8px" }}>{c.forecast_yield ?? "—"}</td>
                          <td style={{ ...tdR, fontWeight: 700, padding: "7px 8px" }}>{c.pred_acres != null ? c.pred_acres.toLocaleString() : "—"}</td>
                          <td style={{ ...tdR, fontWeight: 700, padding: "7px 8px" }}>{c.pred_prod != null ? `${(c.pred_prod / 1e6).toFixed(2)}M` : "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px" }}>{c.weather?.season_precip_in != null ? `${c.weather.season_precip_in}"` : "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px", color: c.weather?.precip_anom == null ? "#888" : Math.abs(c.weather.precip_anom) > 0.30 ? "#b85000" : Math.abs(c.weather.precip_anom) > 0.15 ? "#b08900" : "#5a7a5a", fontWeight: c.weather?.precip_anom != null && Math.abs(c.weather.precip_anom) > 0.15 ? 700 : 400 }}>{c.weather?.precip_anom != null ? `${c.weather.precip_anom > 0 ? "+" : ""}${Math.round(c.weather.precip_anom * 100)}%` : "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px" }}>{c.weather?.season_gdd ?? "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px", color: c.weather?.gdd_anom == null ? "#888" : c.weather.gdd_anom < -0.15 ? "#b85000" : Math.abs(c.weather.gdd_anom) > 0.10 ? "#b08900" : "#5a7a5a", fontWeight: c.weather?.gdd_anom != null && Math.abs(c.weather.gdd_anom) > 0.10 ? 700 : 400 }}>{c.weather?.gdd_anom != null ? `${c.weather.gdd_anom > 0 ? "+" : ""}${Math.round(c.weather.gdd_anom * 100)}%` : "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px", color: !c.drought ? "#888" : c.drought.dsci >= 200 ? "#c0392b" : c.drought.dsci >= 100 ? "#b85000" : c.drought.dsci >= 50 ? "#b08900" : "#3f7d3f", fontWeight: c.drought && c.drought.dsci >= 50 ? 700 : 400 }} title={c.drought?.category}>{c.drought?.dsci != null ? c.drought.dsci : "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px", color: !c.early_stress ? "#888" : c.early_stress.drag_pct >= 4 ? "#b85000" : c.early_stress.drag_pct >= 2 ? "#b08900" : "#3f7d3f", fontWeight: c.early_stress && c.early_stress.drag_pct >= 2 ? 700 : 400 }}>{c.early_stress?.drag_pct != null ? `−${c.early_stress.drag_pct}%` : "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px" }}>{c.pace_factor ?? "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px", color: miDeltaColor((c.total_wx_factor ?? 1) - 1), fontWeight: 700 }} title="Composite weather factor: early-season stress × drought × silking weather. The full weather drag on this county's yield.">{c.total_wx_factor ?? "—"}</td>
                          <td style={{ ...tdR, padding: "7px 8px", color: c.confidence === "med" ? "#3f7d3f" : "#b08900", textTransform: "uppercase", fontSize: 10.5 }}>{c.confidence}</td>
                        </tr>
                        {expanded === c.fips && (
                          <tr><td colSpan={13} style={{ padding: 0, background: "#f7faf7", borderBottom: "1px solid #f0f4f0" }}>
                            {/* ── 5-yr yield bar chart + forecast year ─────────────────── */}
                            <div style={{ padding: "16px 18px 4px" }}>
                              <div style={{ fontSize: 11, color: MI_MUTE, marginBottom: 8, letterSpacing: 0.3, textTransform: "uppercase", fontWeight: 700 }}>
                                {c.name} · last {(c.yld_hist || []).length}y NASS yield + {year} forecast
                              </div>
                              {(() => {
                                const hist = (c.yld_hist || []).slice();
                                const fc = c.forecast_yield != null ? c.forecast_yield : null;
                                if (!hist.length && fc == null) return <div style={{ fontSize: 12, color: MI_MUTE }}>No yield history available for this county.</div>;
                                const all = [...hist.map((h) => h.v), ...(fc != null ? [fc] : [])];
                                const yMax = Math.max(...all) * 1.08;
                                const yMin = Math.min(...all) * 0.85;
                                const range = yMax - yMin || 1;
                                const bars = [...hist, ...(fc != null ? [{ y: year, v: fc, isFc: true }] : [])];
                                const rm = c.recent_5yr_mean;
                                return (
                                  <div>
                                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${bars.length}, 1fr)`, gap: 10, alignItems: "end", height: 110, position: "relative" }}>
                                      {/* 5-yr-avg reference line */}
                                      {rm != null && (
                                        <div style={{ position: "absolute", left: 0, right: 0, top: `${((yMax - rm) / range) * 100}%`, borderTop: "1px dashed #9ab39a", pointerEvents: "none" }}>
                                          <span style={{ position: "absolute", right: 0, top: -16, fontSize: 10.5, color: "#6a8a6a", background: "#f7faf7", padding: "0 4px", fontWeight: 700 }}>5-yr avg {rm}</span>
                                        </div>
                                      )}
                                      {bars.map((b, i) => {
                                        const h = ((b.v - yMin) / range) * 100;
                                        return (
                                          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: 110 }}>
                                            <div style={{ fontSize: 10.5, fontWeight: 700, color: b.isFc ? MI_GREEN : "#5a7a5a", marginBottom: 3 }}>{b.v}</div>
                                            <div style={{ width: "70%", height: `${Math.max(4, h)}%`, background: b.isFc ? MI_GREEN : "#a8c8a8", borderRadius: "3px 3px 0 0", border: b.isFc ? `2px solid ${MI_GREEN}` : "none" }} />
                                            <div style={{ fontSize: 10.5, color: b.isFc ? MI_GREEN : MI_MUTE, fontWeight: b.isFc ? 800 : 500, marginTop: 4 }}>{b.isFc ? `'${String(b.y).slice(2)}f` : `'${String(b.y).slice(2)}`}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                            {/* ── Stat grid (compact, scannable) ───────────────────────── */}
                            <div style={{ padding: "10px 18px 4px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(108px, 1fr))", gap: 10, borderTop: "1px solid #e4ede4", marginTop: 10 }}>
                              {[
                                ["vs 5-yr", c.vs_recent_mean != null ? `${miSigned(c.vs_recent_mean, 1)} bu` : "—", miDeltaColor(c.vs_recent_mean)],
                                // Mechanistic model rides the 7-yr Olympic mean anchor (not a trend projection)
                                // Show the anchor the forecast ACTUALLY used, and name its source.
                                // This tile used to print c.baseline (the trend baseline) even when
                                // the mechanistic path had anchored on something else entirely — so
                                // the number on the card could not be reproduced from the card.
                                // RMA = published expected county yield (insured-acre basis, so it
                                // will not always sit inside the NASS history shown beside it).
                                [c.mech_factor != null
                                  ? (c.anchor_source === "rma_expected" ? `Anchor ${year} · RMA` : `Anchor ${year} · 7yr`)
                                  : `Trend ${year}`,
                                 c.mech_factor != null ? (c.effective_baseline ?? c.baseline ?? "—") : (c.baseline ?? "—"), "#1a2e1a"],
                                ["Pace ×", c.pace_factor ?? "—", "#1a2e1a"],
                                // Wx × = the mechanistic weather factor (THE driver) when active; folds
                                // early-stress + drought + silking into one index vs each cell's normal.
                                ["Wx ×", (c.mech_factor != null ? c.mech_factor : (c.weather?.factor ?? "—")), miDeltaColor(((c.mech_factor != null ? c.mech_factor : c.weather?.factor) ?? 1) - 1)],
                                ["Hist range", `${c.hist_low ?? "—"}–${c.hist_high ?? "—"}${c.hist_n ? ` (${c.hist_n}y)` : ""}`, "#1a2e1a"],
                                ["Pred prod", c.pred_prod != null ? `${(c.pred_prod / 1e6).toFixed(2)}M bu` : "—", MI_GREEN],
                                ["Normal prod", c.normal_prod != null ? `${(c.normal_prod / 1e6).toFixed(2)}M bu` : "—", "#5a7a5a"],
                                ["5-yr harv ac", c.harv_acres_5yr ? c.harv_acres_5yr.toLocaleString() : "—", "#1a2e1a"],
                              ].map(([lb, v, col], i) => (
                                <div key={i}>
                                  <div style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase", color: MI_MUTE, fontWeight: 700 }}>{lb}</div>
                                  <div style={{ fontSize: 14, fontWeight: 800, color: col, lineHeight: 1.2 }}>{v}</div>
                                </div>
                              ))}
                            </div>
                            {/* ── PER-COUNTY WEATHER STORY — full season, this county only.
                                  Each county tracks INDEPENDENTLY: its own GDD, precip,
                                  drought, early stress, silking outlook. Numbers here are
                                  for THIS county, NOT WNY averages. ─────────────────── */}
                            {c.weather && c.weather.season_gdd != null && (
                              <div style={{ padding: "12px 18px 8px", marginTop: 6, borderTop: "1px solid #e4ede4", background: "#f9fcf9" }}>
                                <div style={{ fontSize: 10.5, letterSpacing: 0.5, textTransform: "uppercase", color: "#5a7a5a", fontWeight: 700, marginBottom: 6 }}>
                                  🌦 {c.name} weather — county-specific
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                                  {/* GDD */}
                                  <div>
                                    <div style={{ fontSize: 10, color: MI_MUTE, marginBottom: 2 }}>🔥 GDD to-date</div>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
                                      {c.weather.season_gdd} <span style={{ fontSize: 11, color: MI_MUTE, fontWeight: 500 }}>vs {c.weather.normal_gdd}</span>
                                    </div>
                                    {c.weather.gdd_anom != null && (
                                      <div style={{ fontSize: 11, fontWeight: 700, color: c.weather.gdd_anom > 0 ? "#3f7d3f" : "#b85000" }}>
                                        {c.weather.gdd_anom > 0 ? "+" : ""}{Math.round(c.weather.gdd_anom * 100)}% anom
                                      </div>
                                    )}
                                  </div>
                                  {/* Precip */}
                                  <div>
                                    <div style={{ fontSize: 10, color: MI_MUTE, marginBottom: 2 }}>💧 Precip to-date</div>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
                                      {c.weather.season_precip_in}" <span style={{ fontSize: 11, color: MI_MUTE, fontWeight: 500 }}>vs {c.weather.normal_precip_in}"</span>
                                    </div>
                                    {c.weather.precip_anom != null && (
                                      <div style={{ fontSize: 11, fontWeight: 700, color: Math.abs(c.weather.precip_anom) > 0.15 ? "#b85000" : "#5a7a5a" }}>
                                        {c.weather.precip_anom > 0 ? "+" : ""}{Math.round(c.weather.precip_anom * 100)}% anom
                                      </div>
                                    )}
                                  </div>
                                  {/* Drought (USDM) */}
                                  {c.drought && (
                                    <div>
                                      <div style={{ fontSize: 10, color: MI_MUTE, marginBottom: 2 }}>🌵 USDM drought (wk {c.drought.week || "—"}){c.mech_factor != null ? " · diag" : ""}</div>
                                      <div style={{ fontSize: 14, fontWeight: 800, color: c.drought.dsci >= 200 ? "#c0392b" : c.drought.dsci >= 100 ? "#b85000" : c.drought.dsci >= 50 ? "#b08900" : "#3f7d3f" }}>
                                        DSCI {c.drought.dsci}
                                      </div>
                                      <div style={{ fontSize: 11, color: MI_MUTE }}>{c.drought.category}{(c.drought.drag_pct > 0 && c.mech_factor == null) ? ` · −${c.drought.drag_pct}% yield` : ""}</div>
                                    </div>
                                  )}
                                  {/* Early-season stress */}
                                  {c.early_stress && (
                                    <div>
                                      <div style={{ fontSize: 10, color: MI_MUTE, marginBottom: 2 }}>🌱 Early-stress (Apr-now){c.mech_factor != null ? " · diag" : ""}</div>
                                      <div style={{ fontSize: 14, fontWeight: 800, color: c.early_stress.drag_pct >= 4 ? "#b85000" : c.early_stress.drag_pct >= 2 ? "#b08900" : "#3f7d3f" }}>
                                        −{c.early_stress.drag_pct || 0}%{c.mech_factor != null ? "" : " drag"}
                                      </div>
                                      <div style={{ fontSize: 11, color: MI_MUTE }}>{c.early_stress.stress_days || 0} stress / {c.early_stress.recovery_days || 0} recovery d</div>
                                    </div>
                                  )}
                                  {/* Silking + grain-fill evidence */}
                                  {c.weather.silk_evidence_pct != null && (
                                    <div title="Silking window = plant date + 60–75d. Evidence = % of those days with real observed or forecast data (vs climatology fallback). Rises as the model approaches silking — that's when the forecast sharpens.">
                                      <div style={{ fontSize: 10, color: MI_MUTE, marginBottom: 2 }}>🌽 Silking outlook</div>
                                      <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a" }}>
                                        evidence {c.weather.silk_evidence_pct}%
                                      </div>
                                      <div style={{ fontSize: 11, color: MI_MUTE }}>fill evidence {c.weather.fill_evidence_pct}%</div>
                                    </div>
                                  )}
                                  {/* Weather factor — mechanistic (THE driver) when active. The
                                      early-stress + drought chips above are now DIAGNOSTIC ONLY:
                                      the mechanistic model folds moisture/drought/heat/silking into
                                      this single index vs each cell's climatology normal. */}
                                  <div>
                                    <div style={{ fontSize: 10, color: MI_MUTE, marginBottom: 2 }}>⚙ Weather factor {c.mech_factor != null ? "(mechanistic)" : "(silking)"}</div>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: miDeltaColor(((c.mech_factor != null ? c.mech_factor : c.weather.factor) || 1) - 1) }}>
                                      ×{c.mech_factor != null ? c.mech_factor : (c.weather.factor || 1)}
                                    </div>
                                    <div style={{ fontSize: 11, color: MI_MUTE }}>{c.mech_factor != null ? "folds early-stress + drought + silking" : `${c.weather.days_to_date}d obs · ${c.weather.hist_years}y norm`}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* ── Override + method line ───────────────────────────────── */}
                            <div style={{ padding: "10px 18px 16px", display: "flex", flexWrap: "wrap", gap: "6px 22px", alignItems: "center", fontSize: 11.5, color: MI_MUTE, borderTop: "1px solid #e4ede4" }}>
                              <span style={{ display: "flex", alignItems: "center", gap: 6, color: MI_GREEN2 }}>Override ×
                                <input value={ovr[c.fips] ?? ""} placeholder={String(c.override_factor)} onClick={(e) => e.stopPropagation()} onChange={(e) => setOvr((p) => ({ ...p, [c.fips]: e.target.value }))} style={{ width: 60, padding: "3px 6px", border: "1px solid #ccd", borderRadius: 5, fontSize: 12 }} />
                                <span style={{ fontSize: 10, color: "#9ab" }}>then Recompute</span>
                              </span>
                              {c.baseline_method && <span>Method: {c.baseline_method}</span>}
                              {c.acre_hist && c.acre_hist.length > 0 && (
                                <span>Harv ac: {c.acre_hist.map((h) => `'${String(h.y).slice(2)} ${h.v.toLocaleString()}`).join("  ·  ")}</span>
                              )}
                            </div>
                          </td></tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  {/* WNY aggregate footer — Acres/Prod are TOTALS; Yield is
                      acre-weighted (= total prod ÷ total acres, so it's consistent
                      with those two columns and matches the hero); the condition
                      columns are simple 16-county averages. */}
                  {rows.length > 0 && (() => {
                    const wAc = rows.filter((c) => c.pred_acres != null && c.pred_acres > 0);
                    const totAc = wAc.reduce((s, c) => s + c.pred_acres, 0);
                    const totProd = rows.reduce((s, c) => s + (c.pred_prod || 0), 0);
                    const savg = (sel) => { const v = rows.map(sel).filter((x) => x != null && isFinite(x)); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };
                    const wtdYield = totAc > 0 ? totProd / totAc : null;
                    const rain = savg((c) => c.weather?.season_precip_in);
                    const rAnom = savg((c) => c.weather?.precip_anom);
                    const gdd = savg((c) => c.weather?.season_gdd);
                    const gAnom = savg((c) => c.weather?.gdd_anom);
                    const dsci = savg((c) => c.drought?.dsci);
                    const stress = savg((c) => c.early_stress?.drag_pct);
                    const pace = savg((c) => c.pace_factor);
                    const wx = savg((c) => c.total_wx_factor);
                    const fc = { ...tdR, padding: "9px 8px", borderTop: "2px solid #cfe0cf", fontWeight: 800, color: "#1a2e1a", background: "#eef4ee" };
                    return (
                      <tfoot><tr>
                        <td style={{ padding: "9px 10px", borderTop: "2px solid #cfe0cf", fontWeight: 800, color: "#1a2e1a", background: "#eef4ee" }}>WNY · {rows.length} cty</td>
                        <td style={fc} title="Acre-weighted: total production ÷ total acres">{wtdYield != null ? wtdYield.toFixed(1) : "—"}</td>
                        <td style={fc} title="Total predicted grain acres">{totAc.toLocaleString()}</td>
                        <td style={fc} title="Total predicted production">{totProd > 0 ? `${(totProd / 1e6).toFixed(2)}M` : "—"}</td>
                        <td style={fc}>{rain != null ? `${rain.toFixed(1)}"` : "—"}</td>
                        <td style={fc}>{rAnom != null ? `${rAnom > 0 ? "+" : ""}${Math.round(rAnom * 100)}%` : "—"}</td>
                        <td style={fc}>{gdd != null ? Math.round(gdd) : "—"}</td>
                        <td style={fc}>{gAnom != null ? `${gAnom > 0 ? "+" : ""}${Math.round(gAnom * 100)}%` : "—"}</td>
                        <td style={fc}>{dsci != null ? Math.round(dsci) : "—"}</td>
                        <td style={fc}>{stress != null ? `−${stress.toFixed(1)}%` : "—"}</td>
                        <td style={fc}>{pace != null ? pace.toFixed(3) : "—"}</td>
                        <td style={fc}>{wx != null ? wx.toFixed(3) : "—"}</td>
                        <td style={fc}></td>
                      </tr></tfoot>
                    );
                  })()}
                </table>
              </div>
            )}
          </div>

          {errRows.length > 0 && (
            <div style={{ ...card, fontSize: 12, color: MI_MUTE }}>
              No estimate: {errRows.map((c) => `${c.name} (${c.error})`).join(" · ")}
            </div>
          )}

          {/* ── Model Lab — hidden in the facelift (Kevin: "we don't need it anymore").
              The model still computes every input; this read-only transparency
              panel is gated off, not deleted, so it can return as a follow-up. ── */}
          {false && (
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div onClick={() => setShowLab((s) => !s)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer", borderBottom: showLab ? "1px solid #eef3ee" : "none" }}>
              <span style={{ fontWeight: 800, color: "#1a2e1a" }}>{showLab ? "▾" : "▸"} 🔬 Model Lab — every input behind the prediction</span>
              <span style={{ fontSize: 11, color: MI_MUTE }}>{showLab ? "" : "open to inspect"}</span>
            </div>
            {showLab && (
              <div style={{ padding: "14px 18px", fontSize: 12.5, color: MI_GREEN2, lineHeight: 1.65 }}>
                {(() => {
                  const b = miRollup(baseData), s = miRollup(data);
                  const dFc = b && s && b.fc != null && s.fc != null ? Number((s.fc - b.fc).toFixed(1)) : null;
                  const aHeat = baseData && baseData.wx_anom ? Math.round(baseData.wx_anom.gA * 100) : 0;
                  const aRain = baseData && baseData.wx_anom ? Math.round(baseData.wx_anom.pA * 100) : 0;
                  const aAcres = baseData ? Math.round((baseData.acres_actual_pct || 0) * 100) : 0;
                  return (
                    <div style={{ border: "1px solid #dde7dd", borderRadius: 10, padding: 14, marginBottom: 18, background: "#f7faf7" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <span style={{ fontWeight: 800, color: "#1a2e1a", fontSize: 13 }}>What-if scenario</span>
                        <button onClick={() => setLab(miDefaultLab())} style={{ fontSize: 11, fontWeight: 700, border: "1px solid #ccd", background: "#fff", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>↺ Reset to Clover Ag defaults</button>
                      </div>
                      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                        {[["Clover Ag base", b, "#6b7e5e", false], ["Your scenario", s, MI_GREEN, true]].map(([nm, R, col, sc], i) => (
                          <div key={i} style={{ flex: "1 1 170px", background: "#fff", border: `1px solid ${sc ? "#bcd9bc" : "#e4ece4"}`, borderRadius: 8, padding: "10px 12px" }}>
                            <div style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: "#9ab" }}>{nm}</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: col }}>{R && R.fc != null ? R.fc : "—"}<span style={{ fontSize: 12, color: "#9ab", fontWeight: 600 }}> bu/ac</span></div>
                            <div style={{ fontSize: 11.5, color: "#6b7e5e" }}>{R && R.prodM != null ? `${R.prodM.toFixed(1)}M bu` : "—"}{R && R.pct != null ? ` · ${R.pct >= 0 ? "▲" : "▼"}${Math.abs(Math.round(R.pct * 100))}% vs normal` : ""}</div>
                          </div>
                        ))}
                        {dFc != null && (
                          <div style={{ flex: "0 0 auto", textAlign: "center", padding: "0 4px" }}>
                            <div style={{ fontSize: 10, textTransform: "uppercase", color: "#9ab" }}>Δ yield</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: dFc > 0 ? "#3f7d3f" : dFc < 0 ? "#c0392b" : "#6b7e5e" }}>{dFc > 0 ? "+" : ""}{dFc}</div>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px 22px" }}>
                        <MiKnob label="Planted acres vs normal" val={lab.acresPct != null ? lab.acresPct : aAcres} min={-50} max={20} step={1} fmt={(x) => `${x > 0 ? "+" : ""}${x}%`} hint="Grain acres vs the 5-yr county normal. Lower = late planting / prevent-plant shrinks the crop. Starts at what your survey + the forecast imply." onChange={(v) => setLab((L) => ({ ...L, acresPct: v }))} />
                        <MiKnob label="Rainfall vs normal" val={lab.rainPct != null ? lab.rainPct : aRain} min={-60} max={100} step={5} fmt={(x) => `${x > 0 ? "+" : ""}${x}%`} hint="Season precip vs the 16-yr normal. Below = drought stress; well above = too-wet stress. Starts at the actual to-date + 14-day forecast." onChange={(v) => setLab((L) => ({ ...L, rainPct: v }))} />
                        <MiKnob label="Heat (GDD) vs normal" val={lab.heatPct != null ? lab.heatPct : aHeat} min={-30} max={30} step={1} fmt={(x) => `${x > 0 ? "+" : ""}${x}%`} hint="Growing degree-days vs the 16-yr normal. Cool spring = slower crop. Starts at the actual season-to-date. (Heat effect on yield is capped ±12%.)" onChange={(v) => setLab((L) => ({ ...L, heatPct: v }))} />
                      </div>
                      <div style={{ fontSize: 10.5, color: "#9ab", marginTop: 8 }}>Each slider starts at what's <i>actually</i> happening (so scenario = our estimate). Drag one to ask "what if" — hero, table, charts &amp; the scenario card recompute instantly. <b>Yield/ac is clamped</b> (weather ±12%, baseline to each county's realized range) so big swings land mostly in the <b>production / % vs normal</b> number. ↺ Reset returns every slider to actual.</div>
                    </div>
                  );
                })()}
                {data.backtest && (
                  <div style={{ border: "1px solid #cfe0cf", background: "#eef5ee", borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
                    <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 4 }}>📏 Accuracy — trend baseline only, walk-forward backtest</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 22px", fontSize: 13, color: "#1a2e1a" }}>
                      <span><b style={{ color: "#2d6a2d", fontSize: 16 }}>±{data.backtest.mae}</b> bu/ac MAE</span>
                      <span>bias <b>{data.backtest.bias >= 0 ? "+" : ""}{data.backtest.bias}</b> bu/ac</span>
                      <span><b>{data.backtest.w20}%</b> within 20 bu</span>
                      <span><b>{data.backtest.w10}%</b> within 10 bu</span>
                      <span style={{ color: MI_MUTE }}>{data.backtest.n} county-years, {data.backtest.fromY}–{data.backtest.toY}</span>
                    </div>
                    <div style={{ fontSize: 10.5, color: MI_MUTE, marginTop: 5 }}><b style={{ color: "#8a5a1a" }}>⚠ This does not measure the forecast above.</b> Each county-year is predicted using <i>only</i> the years before it (no peeking), but what it validates is the <b>trend baseline</b> — and the shipped forecast no longer anchors on it. Every county whose anchor reads <b>RMA expected</b> bypasses this code path entirely (currently {(data.counties || []).filter((c) => c.anchor_source === "rma_expected").length} of {(data.counties || []).length}). Weather, planting-pace and the mechanistic engine are overlays on top and aren't backtested either (no multi-year survey/forecast history). Treat these as a floor on baseline error, <i>not</i> as the model's accuracy. A frozen-model walk-forward at true as-of dates is the honest replacement and hasn't been built. Reflects the current slope-cap ({lab.slopeCap} bu/yr).</div>
                  </div>
                )}
                <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 2 }}>1 · Trend baseline</div>
                <div style={{ marginBottom: 10 }}>NASS county grain yield, OLS fit, slope-capped ≤ 2.5 bu/ac/yr, anchored on the recent 5-yr level &amp; clamped to each county's realized min–max range, projected to {year}. Per-county method is in each row's detail (e.g. "OLS trend (10 yrs), capped").</div>

                <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 2 }}>2 · Planting pace (your survey → date-windowed loss)</div>
                <div style={{ marginBottom: 4 }}>{data.statewide_pace?.note || "—"}</div>
                <div style={{ marginBottom: 10, color: MI_MUTE }}>Curve — 100% ≤ {MI_CORN_PLANTING.optimalEndMonth}/{MI_CORN_PLANTING.optimalEndDay}; then {MI_CORN_PLANTING.segments.map((s) => `${(s.rate * 100).toFixed(1)}%/day→${s.m}/${s.d}`).join(", ")}; floor {Math.round(MI_CORN_PLANTING.floor * 100)}%; unplanted projected over {MI_CORN_PLANTING.projectionDays} workable days (forecast-aware, rained out ≥ {MI_CORN_PLANTING.rainBlockIn}").</div>

                <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 2 }}>3 · Weather / GDD</div>
                <div style={{ marginBottom: 10 }}>{wny ? `${wny.wxCov}/${wny.n} counties with a live weather factor (modified base-50 GDD + precip vs 16-yr climatology, season-to-date). ` : ""}Sensitivities: GDD {MI_COEF.GDD_SENS} (effect capped ±{MI_COEF.GDD_CLAMP}), drought {MI_COEF.DRY_SENS}, wet {MI_COEF.WET_SENS} beyond +{Math.round(MI_COEF.WET_THRESH * 100)}%; weather factor floored/ceiled {MI_COEF.WEATHER_FLOOR}/{MI_COEF.WEATHER_CEIL}. The Rainfall &amp; Heat sliders above set these inputs to any % vs normal.</div>

                <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 2 }}>4 · Prevent-plant / acreage</div>
                <div style={{ marginBottom: 10 }}>{data.production ? `Production = forecast yield × (5-yr avg harvested acres × ${data.production.planted_by_fpd_pct}% projected planted by corn FPD ${data.production.fpd}). ${data.production.pp_ac ? `~${data.production.pp_ac.toLocaleString()} ac prevent-plant risk. ` : ""}WNY total ${(data.production.pred_bu / 1e6).toFixed(2)}M bu vs ${(data.production.normal_bu / 1e6).toFixed(2)}M normal.` : "—"}</div>

                <div style={{ fontWeight: 800, color: "#1a2e1a", marginBottom: 2 }}>5 · Silage / cow displacement</div>
                {data.cow_silage ? (
                  <div style={{ marginBottom: 10 }}>
                    {(MI_REGIONS[region] || {}).label || "Regional"} herd <b>{data.cow_silage.currentCows.toLocaleString()}</b> milking cows{data.cow_silage.regionCounties ? ` (${data.cow_silage.regionCounties} counties)` : ""} (vs <b>{data.cow_silage.normalCows.toLocaleString()}</b> 5-yr avg) need <b>~{data.cow_silage.tonsPerCow}</b> tons silage/cow/yr.
                    At a stressed silage yield of <b>{data.cow_silage.stressedSilageYield} t/ac</b> (normal {data.cow_silage.normalSilageYield}, hit by this season's weather), required silage = <b>{data.cow_silage.requiredAcres.toLocaleString()} ac</b> vs normal {data.cow_silage.normalAcres.toLocaleString()} → <b style={{ color: data.cow_silage.extraAcres > 0 ? "#c0392b" : "#3f7d3f" }}>{data.cow_silage.extraAcres > 0 ? "+" : ""}{data.cow_silage.extraAcres.toLocaleString()} ac</b> pulled from grain ({data.silage_displace_pct.toFixed(1)}% of NY grain acres). NY grain share of all corn ≈ {data.production && data.production.grain_share_pct != null ? `${data.production.grain_share_pct.toFixed(0)}%` : "—"}.
                    {data.cow_silage.calibrationDetail && data.cow_silage.calibrationDetail.length > 0 && (
                      <div style={{ marginTop: 8, background: "#fff", border: "1px solid #e4ece4", borderRadius: 8, padding: "8px 10px" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#1a2e1a", marginBottom: 4 }}>Showing the work — {data.cow_silage.calibrationDetail.length}-yr tons/cow calibration (NY state)</div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                          <thead><tr style={{ color: MI_MUTE }}>
                            <th style={{ textAlign: "left", padding: "2px 6px" }}>Year</th>
                            <th style={{ textAlign: "right", padding: "2px 6px" }}>Silage acres</th>
                            <th style={{ textAlign: "right", padding: "2px 6px" }}>Yield (t/ac)</th>
                            <th style={{ textAlign: "right", padding: "2px 6px" }}>Milk cows</th>
                            <th style={{ textAlign: "right", padding: "2px 6px" }}>Implied t/cow</th>
                          </tr></thead>
                          <tbody>
                            {data.cow_silage.calibrationDetail.map((d) => (
                              <tr key={d.year} style={{ borderTop: "1px solid #f0f4f0" }}>
                                <td style={{ padding: "2px 6px" }}>{d.year}</td>
                                <td style={{ padding: "2px 6px", textAlign: "right" }}>{d.silageAcres.toLocaleString()}</td>
                                <td style={{ padding: "2px 6px", textAlign: "right" }}>{d.silageYield}</td>
                                <td style={{ padding: "2px 6px", textAlign: "right" }}>{d.cows.toLocaleString()}</td>
                                <td style={{ padding: "2px 6px", textAlign: "right", fontWeight: 700, color: "#2d6a2d" }}>{d.tonsPerCow}</td>
                              </tr>
                            ))}
                            <tr style={{ borderTop: "2px solid #cfe0cf", background: "#f7faf7" }}>
                              <td colSpan={4} style={{ padding: "3px 6px", textAlign: "right", fontWeight: 700, color: "#1a2e1a" }}>Average → tons/cow used</td>
                              <td style={{ padding: "3px 6px", textAlign: "right", fontWeight: 800, color: "#2d6a2d" }}>{data.cow_silage.tonsPerCow}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div style={{ color: MI_MUTE, marginTop: 6 }}>Applied uniformly to WNY county grain acres; production above already reflects this. Lower silage yield (stress) or higher cow numbers → bigger displacement.</div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 10, color: MI_MUTE }}>NASS silage data not yet loaded.</div>
                )}

                <div style={{ marginTop: 8, padding: "10px 12px", background: "#fff7e8", border: "1px solid #e6d3a3", borderRadius: 8, color: "#7a5b00", fontSize: 11.5 }}>
                  Sliders + per-layer charts + side-by-side base-vs-scenario are all live above. Cow-silage displacement is now in the production number, calibrated on NY history.
                </div>
              </div>
            )}
          </div>
          )}
        </>
      )}
    </div>
  );
}

const GRAIN_TICKER = { Corn: 'ZC', Soybeans: 'ZS', Wheat: 'ZW' };

// Local elevators whose daily-scraped board (basis_calculated_daily) can value
// unpriced bushels on the Forward Price Curve as a "Delivered" destination. Each
// must post to basis_calculated_daily under `source`. Add a row to offer another.
const LOCAL_ELEVATORS = [
  { crop: 'Corn',  commodity: 'corn',  source: 'wny_energy',   label: 'Medina — WNY Energy' },
  { crop: 'Wheat', commodity: 'wheat', source: 'star_of_west', label: 'Churchville — Star of the West' },
];

// ── Naming the boards, and which one a farmer is actually selling to ──
// basis_calculated_daily keys every board by a SOURCE SLUG, which is not a name and not
// stable. Howlett's daily snapshot (api/_howlett-basis.js) slugs the column header straight
// off the photographed sheet, so ONE physical delivery point arrives under several
// spellings — 'howlett_avon_lkville' on the corn sheet, 'howlett_avon_lakevlle' on wheat —
// and any re-worded column mints a NEW slug whose rows then sit in the ten-day window
// looking like a second elevator. Raw slugs also read badly ("howlett dry p u east of lima").
//
// So this maps slug → { house, place, key, rank }:
//   key  collapses the spellings of one real bid point (freshest slug wins the entry)
//   rank puts HOWLETT FIRST. That's not cosmetic — it's the default cash number. Medina's
//        corn basis runs ~20¢ over Howlett's and ~25–30¢ of that is haul (see api/_elevator.js),
//        so defaulting to the widest board quoted a target the farmer can't actually net.
const GRAIN_HOWLETT_TOWN_LBL = { avon: 'Avon & Lakeville', leroy: 'LeRoy & TD Ag', batavia: 'Batavia', geneva: 'Geneva' };
const GRAIN_HOWLETT_TOWN_RANK = { avon: 0, leroy: 1, batavia: 2, geneva: 3 };
const grainTitle = (s) => String(s || '').replace(/\bp u\b/g, 'P/U').replace(/\b[a-z]/g, c => c.toUpperCase());
function grainElevator(src) {
  const s = String(src || '');
  if (/^howlett/.test(s)) {
    const rest = s.replace(/^howlett_?/, '').replace(/_/g, ' ');
    const town = howlettTown(rest);
    // No town in the header = a farm-pickup column (P/U West, Dry P/U East of Lima,
    // Picked Up). Same house, but Howlett trucks it, so the basis is a different number
    // and it must never be mistaken for the delivered bid.
    return town
      ? { house: 'Howlett', place: GRAIN_HOWLETT_TOWN_LBL[town], term: 'delivered', key: 'howlett|' + town, rank: GRAIN_HOWLETT_TOWN_RANK[town] }
      : { house: 'Howlett', place: grainTitle(rest), term: 'pickup', key: 'howlett|pu|' + rest.replace(/\s+/g, ''), rank: 4 };
  }
  if (s === 'wny_energy')   return { house: 'WNY Energy',       place: 'Medina',      term: 'delivered', key: s, rank: 6 };
  if (s === 'star_of_west') return { house: 'Star of the West', place: 'Churchville', term: 'delivered', key: s, rank: 6 };
  if (s === 'adm_buffalo')  return { house: 'ADM',              place: 'Buffalo',     term: 'delivered', key: s, rank: 6 };
  if (/^deerfield_/.test(s)) return { house: 'Deerfield Ag', place: grainTitle(s.replace(/^deerfield_/, '').replace(/_/g, ' ')) + ', OH', term: 'delivered', key: s, rank: 8 };
  return { house: grainTitle(s.replace(/_/g, ' ')), place: '', term: 'delivered', key: s, rank: 7 };
}
const grainElevatorLabel = (e) => e.place ? `${e.house} — ${e.place}${e.term === 'pickup' ? ' · picked up' : ''}` : e.house;
const GRAIN_TERM_LBL = { delivered: 'Delivered', pickup: 'Picked up' };

// COP category schemas, per-crop pre-fill defaults and the per-pass key list now
// live in src/copRules.js — ONE definition, shared with the SILAS shadow cost
// estimator so it cannot grow a second set of economics (Farm Economics 2A).
// Imported at the top of this file; nothing here re-declares them.

// Says whether a yield is ESTIMATED or ACTUAL, everywhere one is shown. Green =
// harvested fact; amber = a forecast. Reads a `yieldInfo()` result — never a bare
// number, so the label can't drift from the figure it describes.
function YieldBasisChip({ info, showNote = true }) {
  if (!info) return null;
  const actual = info.basis === 'actual';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                     padding: '1px 6px', borderRadius: 999, border: '1px solid',
                     background: actual ? '#e8f4e4' : '#fdf6e3',
                     color:      actual ? '#1f7a3d' : '#8a6a00',
                     borderColor: actual ? '#bcdcb4' : '#e8d9a8' }}>{info.label}</span>
      {showNote && info.note && <span style={{ fontSize: 10.5, color: '#677167' }}>{info.note}</span>}
    </span>
  );
}


// Compact SVG line chart used across the Markets panels. points: [{ y }] (+optional label).
function GrainTinyChart({ points, height = 90, color = '#2d6a1a', fill = true, zeroLine = false }) {
  const W = 320, H = height, pad = 8;
  const ys = (points || []).map(p => Number(p.y)).filter(v => !isNaN(v));
  if (ys.length < 1) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b3c0b0', fontSize: 11, fontStyle: 'italic' }}>No history yet — builds daily</div>;
  let min = Math.min(...ys), max = Math.max(...ys);
  if (zeroLine) { min = Math.min(min, 0); max = Math.max(max, 0); }
  if (min === max) { const d = Math.abs(min) * 0.1 + 1; min -= d; max += d; }
  const n = points.length;
  const xAt = i => (n <= 1 ? W / 2 : pad + (i * (W - 2 * pad)) / (n - 1));
  const yAt = v => H - pad - ((v - min) / (max - min)) * (H - 2 * pad);
  const pts = points.map((p, i) => `${xAt(i).toFixed(1)},${yAt(Number(p.y)).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {zeroLine && 0 >= min && 0 <= max && <line x1={pad} y1={yAt(0)} x2={W - pad} y2={yAt(0)} stroke="#ccc" strokeDasharray="3 3" strokeWidth="1" />}
      {fill && <polygon points={`${xAt(0)},${H - pad} ${pts} ${xAt(n - 1)},${H - pad}`} fill={color} opacity="0.10" />}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" />
      {n <= 8 && points.map((p, i) => <circle key={i} cx={xAt(i)} cy={yAt(Number(p.y))} r="2.5" fill={color} />)}
    </svg>
  );
}

// ── Grain futures market context: reference levels + seasonal schedule ──
// ⚠ 2026-09-01: this file no longer tells a farmer when to sell. What used to be
// "disciplined resting-order targets" is analytical reference context; a TARGET
// exists only where a farmer established one, and Silas administers those.
// Uses futures_history_daily (10yr OHLCV) for market CONTEXT — never price prediction.
// Everything is expressed in FUTURES terms (elevator-agnostic): the farmer places
// reference levels are expressed in futures terms; the elevator's basis sets cash.
//   • (removed 2026-09-01) a "sell now" tranche sized off the 1yr range — a when-to-sell call
//   • Levels = prior highs (3mo/6mo/52wk), shown as reference context, never as orders to place
//   • Seasonal milestone calendar = the time backstop so unpriced bushels don't ride into harvest
// Insurance ceiling: never forward-price more than the RP-guaranteed bushels (noted; per-farm cap plugs in).
const GRAIN_PLAN_CAL = {   // cumulative % of the crop sold by each point in the CROP-YEAR marketing timeline
  // sched = [monthsRelativeToHarvest, cumulative% target]. POSITIVE = before harvest, 0 = harvest,
  // NEGATIVE = after harvest (marketing stored/delivered grain into the next year). Pegged to the crop
  // year + today's date, NOT the board (a deferred board like WH27 still markets the prior harvested crop).
  // This is the DISCIPLINE FLOOR — the *minimum* you sell by each date so nothing rides unpriced, NOT an
  // aggressive plan. It is deliberately CONSERVATIVE pre-harvest (don't force-sell an unmade crop; the
  // resting-order TARGETS capture a pre-harvest rally opportunistically when the market offers a level).
  // Bulk of the floor lands AT/AFTER harvest once the crop is made; finishes ~9 mo out, before the next
  // harvest. Starts 12 mo out (small) since with bins you're marketing old + new crop concurrently.
  // Delivery-INDEPENDENT: pricing is year-round no matter when you deliver (delivery drives board/carry only).
  Wheat:    { plantLbl:"Oct plant", steps:[["by planting",12],["by Mar 1",30],["by May 1",40],["by Jul harvest",50]], peakMo:5, harvestMo:7,
    sched:[[12,8],[7,18],[4,30],[2,40],[0,50],[-3,72],[-6,88],[-9,95]] },
  Corn:     { plantLbl:"May plant", steps:[["by planting",15],["by Aug 1",30],["by harvest",42]], peakMo:5, harvestMo:11,
    sched:[[12,5],[7,15],[4,25],[2,33],[0,42],[-3,62],[-6,82],[-9,93]] },
  Soybeans: { plantLbl:"May plant", steps:[["by planting",15],["by Aug 1",30],["by harvest",42]], peakMo:2, harvestMo:10,
    sched:[[12,5],[7,15],[4,25],[2,33],[0,42],[-3,62],[-6,82],[-9,93]] },
};
// Months from today until the crop-year's harvest (negative = already harvested / storing).
const grainMonthsToHarvest = (crop, cropYear) => {
  const cal = GRAIN_PLAN_CAL[crop]; if (!cal || cropYear == null) return null;
  const now = new Date();
  return (cropYear - now.getUTCFullYear()) * 12 + (cal.harvestMo - (now.getUTCMonth() + 1));
};
// DELIVERY-INDEPENDENT schedule (Kevin 2026-07-11). Pricing is year-round no matter
// WHEN you physically deliver — delivering at harvest does NOT force pricing by harvest
// (you price open-storage bushels over the year just the same). So ONE schedule for all
// delivery modes; the delivery toggle only drives board + carry, never the pace.
function grainSchedFor(cal, _storageMode) {
  return cal.sched;
}
// The plan's cumulative %-sold target for a CROP YEAR at today's date (interpolated on the
// harvest-relative schedule). `cap` = pre-harvest ceiling (never forward-price past the
// insured guarantee — Kevin's rule); it only clips PRE-harvest values (months > 0).
function grainSchedTarget(crop, cropYear, storageMode = null, cap = 100) {
  const cal = GRAIN_PLAN_CAL[crop]; if (!cal) return null;
  const s = grainSchedFor(cal, storageMode);
  const months = grainMonthsToHarvest(crop, cropYear); if (months == null) return null;
  const m = Math.max(-12, Math.min(12, months));
  let val;
  for (let i = 0; i < s.length - 1; i++) { const [m1, p1] = s[i], [m2, p2] = s[i + 1]; if (m <= m1 && m >= m2) { val = p1 + (p2 - p1) * (m1 - m) / (m1 - m2); break; } }
  if (val == null) val = m > s[0][0] ? s[0][1] : s[s.length - 1][1];
  return months > 0 ? Math.min(val, cap) : val;   // pre-harvest capped at the guarantee
}
// Full "% sold by date" roadmap for a crop year — the sched curve's harvest-relative
// months rendered as concrete calendar milestones, each flagged past/upcoming vs today,
// plus today's interpolated target. This is the universal backstop the resting-order
// targets pull sales FORWARD against; surfaced so the schedule is legible, not just one #.
function grainScheduleRows(crop, cropYear, storageMode = null, cap = 100) {
  const cal = GRAIN_PLAN_CAL[crop]; if (!cal || cropYear == null) return null;
  const MON = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const nowM = grainMonthsToHarvest(crop, cropYear);   // + = months before harvest, − = storing
  const base = cropYear * 12 + (cal.harvestMo - 1);     // absolute month index of harvest
  const rows = grainSchedFor(cal, storageMode).map(([m, pct]) => {
    const t = base - m, yr = Math.floor(t / 12), mo = ((t % 12) + 12) % 12 + 1;
    return { m, pct: m > 0 ? Math.min(pct, cap) : pct, label: `${MON[mo]} '${String(yr).slice(2)}`,   // pre-harvest capped at the guarantee
      phase: m > 0 ? 'pre' : m === 0 ? 'harvest' : 'store',
      past: nowM != null && m > nowM };                 // this milestone's date is already behind us
  });
  return { rows, nowM, nowTarget: grainSchedTarget(crop, cropYear, storageMode, cap), peakMo: cal.peakMo };
}
// ordinal suffix: 1→1st, 2→2nd, 3→3rd, 82→82nd, 11/12/13→th
const grainOrd = (n) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`; };
function grainMarketContext(rows) {
  const s = (rows || []).filter(r => r.close != null).map(r => ({ d: r.bar_date, c: +r.close, h: r.high != null ? +r.high : +r.close }));
  if (s.length < 60) return null;
  const closes = s.map(x => x.c), last = closes[closes.length - 1];
  const lastN = n => closes.slice(Math.max(0, closes.length - n));
  const maxH = n => Math.max(...s.slice(Math.max(0, s.length - n)).map(x => x.h));
  const pctileOf = (arr, v) => arr.length ? arr.filter(x => x <= v).length / arr.length : null;
  const qOf = (arr, p) => { if (!arr.length) return null; const a = arr.slice().sort((x, y) => x - y); const i = (a.length - 1) * p, lo = Math.floor(i), hi = Math.ceil(i); return lo === hi ? a[lo] : a[lo] + (a[hi] - a[lo]) * (i - lo); };
  const win1y = lastN(252);
  const stdev = a => { if (a.length < 2) return null; const m = a.reduce((x, y) => x + y, 0) / a.length; return Math.sqrt(a.reduce((x, y) => x + (y - m) * (y - m), 0) / (a.length - 1)); };
  const lr = []; for (let i = 1; i < closes.length; i++) lr.push(Math.log(closes[i] / closes[i - 1]));
  const vol20 = lr.length >= 20 ? stdev(lr.slice(-20)) * Math.sqrt(252) : null;
  const volSeries = []; for (let i = 20; i < lr.length; i++) { const v = stdev(lr.slice(i - 20, i)); if (v != null) volSeries.push(v * Math.sqrt(252)); }
  const volPctile = (vol20 != null && volSeries.length) ? pctileOf(volSeries.slice(-756), vol20) : null;
  const byMo = {}; s.forEach(x => { const mo = +x.d.slice(5, 7); (byMo[mo] = byMo[mo] || []).push(x.c); });
  const yrAvg = closes.reduce((a, b) => a + b, 0) / closes.length;
  const seas = {}; for (let mo = 1; mo <= 12; mo++) seas[mo] = (byMo[mo] && byMo[mo].length) ? (byMo[mo].reduce((a, b) => a + b, 0) / byMo[mo].length) / yrAvg : null;
  const win3y = lastN(756);
  return { last, pctile1y: pctileOf(win1y, last), lo1y: Math.min(...win1y), hi1y: Math.max(...win1y),
    q25_1y: qOf(win1y, 0.25), q50_1y: qOf(win1y, 0.5), q75_1y: qOf(win1y, 0.75),
    q25_3y: qOf(win3y, 0.25), q50_3y: qOf(win3y, 0.5), q75_3y: qOf(win3y, 0.75),
    lo3y: Math.min(...win3y), hi3y: Math.max(...win3y), pctile3y: pctileOf(win3y, last),
    // ⚠ THE SHAPE IS WHAT SEPARATES THESE TWO BARS, and without it they can draw
    // IDENTICALLY while stating different numbers. Measured on live front-month wheat
    // 2026-09-10: the 3-year window's low ($4.95, 2025-09-10) AND its high ($7.825,
    // 2026-09-01) BOTH fall inside the last 252 sessions — the older 504 sessions ran
    // 4.98-7.00, strictly inside — so lo1y/hi1y and lo3y/hi3y are equal to the cent and
    // the dot lands in the same pixel on both. Only the DISTRIBUTIONS differ, and that
    // difference IS the 96th vs the 98th. That is genuine data, not a bug: do not go
    // looking for a window that is failing to widen.
    dist1y: grainCloseHist(win1y), dist3y: grainCloseHist(win3y),
    hi3m: maxH(63), hi6m: maxH(126), hi52w: maxH(252), vol20, volPctile, seas, asOf: s[s.length - 1].d, n: s.length };
}
const GRAIN_HARVEST_MO = { Wheat: 7, Corn: 12, Soybeans: 11 };   // SRW wheat=Jul, corn=Dec, soy=Nov
// Cost-of-production STARTING POINTS for the shareable Market Intelligence view, taken from
// the Model Farm's 2026 budget. That view was retired 2026-07-29, so THESE LITERALS ARE NOW
// THE SOURCE — they are no longer mirroring a maintained record anywhere. Update them here,
// or point the card at the COP engine, before trusting them for another crop year.
// Editable on the card, because the farmer you're talking to has his
// own numbers. Wheat carries a straw credit: omitting it overstates wheat's cost by ~$1.11/bu
// and makes the crop look unsellable at a profit.
const GRAIN_COP_DEFAULTS = {
  Corn:     { cost: 823, yield: 180, byproduct: 0 },
  Wheat:    { cost: 591, yield: 90,  byproduct: 100 },
  Soybeans: { cost: 520, yield: 49,  byproduct: 0 },
};
// The target ENGINE now lives in src/grainTargets.js — ONE ladder shared by this view and
// the Model Farm order book (api/_grain-wizard.js requires the same file). It used to be
// duplicated here with different math, so the same board gave two different answers.
// grainTechTargets below is presentation-side only: it takes the shared levels and sizes
// each rung as a % of the crop for the personal marketing plan.
function grainTechTargets(tc, priced = null, paceFloor = 0, cap = 100, curve = null, horizon = 126) {
  const { levels: spaced, up, offHigh, trendMA } = grainTargetLadder(tc, curve, horizon);
  // ── AMBITION: total % of the crop to target pre-harvest via this plan ──
  // Scales with price PERCENTILE (sell more when the price is high in its range,
  // LESS when it's low — don't dump the crop into weakness), but never below the
  // seasonal schedule's pace-to-date (`paceFloor`) so we still keep pace as harvest
  // nears. This is the fix for "targeting 90% at the 18th %ile": at a low price the
  // plan prices a modest slice on strength and leaves the rest to the schedule/carry.
  const pctAmb = tc.pctile >= 0.8 ? 90 : tc.pctile >= 0.65 ? 80 : tc.pctile >= 0.5 ? 70 : tc.pctile >= 0.35 ? 58 : tc.pctile >= 0.2 ? 46 : 34;
  // `cap` = the pre-harvest ceiling (never sell more than the insured guarantee before
  // harvest — Kevin's rule). 100 = no cap (post-harvest / insured basis). Ambition and
  // the total targeted both respect it, so the plan never forward-prices past coverage.
  const CAP = Math.max(0, Math.min(100, cap));
  const ambition = Math.min(95, CAP, Math.max(pctAmb, paceFloor || 0));
  const already = (priced != null && !isNaN(priced)) ? Math.max(0, Math.min(100, priced)) : 0;
  const activeTarget = Math.max(0, ambition - already);        // what the plan actively wants priced NOW (price-scaled pace)
  const unpriced = Math.max(0, CAP - already);                 // don't target beyond the pre-harvest cap
  // ALWAYS offer target levels for whatever is unpriced. Behind/on pace → ACTIVE targets
  // sized to the price-scaled pace. Already ahead of pace → the levels stay, framed as
  // OPTIONAL UPSIDE for the rest (sell into strength — all resistance ABOVE the market,
  // so nothing sells unless it rallies). Only a fully-priced (100%) farmer sees no rungs.
  // "Behind pace" = activeTarget ≥ 8 (a meaningful catch-up). At/near/ahead of pace
  // (activeTarget < 8, incl. the 54%-vs-55% case that used to round to a 0% rung) →
  // show targets for ALL the remaining unpriced bushels. Never a 0-bushel target.
  const upside = activeTarget < 8 && unpriced > 0;
  const pool = upside ? unpriced : activeTarget;
  // bank some at market only when high in range AND on the active plan (never nudge an at/ahead-of-pace farmer to sell at market)
  const nowBase = upside ? 0 : (tc.pctile > 0.75 ? 30 : tc.pctile > 0.6 ? 20 : tc.pctile > 0.45 ? 10 : 0);
  const nowPct = Math.min(pool, nowBase);
  const rungPool = Math.max(0, pool - nowPct);
  // ── VARIABLE rung count: fewer rungs when there's little left to spread ──
  let nRungs = rungPool <= 0 ? 0 : rungPool < 22 ? 1 : rungPool < 45 ? 2 : 3;
  nRungs = Math.min(nRungs, spaced.length);
  const rungs = spaced.slice(0, nRungs);
  // per-rung %, floored to 5 but never 0 when there's a pool, and capped so a single
  // reachable level never carries the whole crop (rest waits for higher levels / schedule).
  const per = rungs.length ? Math.max(5, Math.min(30, Math.round(rungPool / rungs.length / 5) * 5)) : 0;
  return { nowPct, rungs: rungs.map(r => ({ ...r, pct: per })), regime: up ? 'uptrend' : 'downtrend', offHigh, trendMA, already, ambition, remaining: pool, mode: upside ? 'upside' : 'active' };
}

// The marketing CROP YEAR a futures contract belongs to. % sold is a crop-year
// figure, not a board figure: a deferred board (e.g. wheat WH27, Mar '27) still
// prices the PRIOR crop being stored, so its crop year is the harvest before its
// delivery month. cropYear = delivery year if the month is at/after that crop's
// new-crop (harvest) month, else the year before. New-crop months: wheat Jul(7),
// corn Dec(12), soy Nov(11).
//
// ── FOUR new-crop-month tables live in this app, and they are NOT interchangeable ──
// Each answers a different question, so each has different months. Before changing
// any one of them, read all four — a mismatch here is how a Sept-delivery soybean
// contract landed on the wrong crop year and vanished from the dashboard.
//
//   TABLE                        WHERE                          MAPS            Wheat Corn Soy
//   GRAIN_NEWCROP_MO             here                           BOARD → year      7   12   11
//     "which crop does this FUTURES CONTRACT price?" — CBOT delivery months. A
//     deferred board (WH27, Mar '27) still prices the 2026 crop sitting in the bin,
//     so its crop year is the harvest BEFORE its delivery month.
//   _NEW_CROP_MONTH_FOR_CROP     deriveContractCropYear         CONTRACT → year   7   10    9
//     "which crop does this SALE cover?" — the grower's SHIP MONTH. Soy is 9 (the
//     USDA soybean marketing year opens Sept 1 and NY beans start coming off then),
//     wheat 7.
//
//     ⚠ WHEAT WAS 6 UNTIL 2026-09-03, and the reason it changed is that the
//     rationale written here did not follow from its own premise: it said
//     "winter wheat is combined in July, SO a June ship is already new crop".
//     If the crop is combined in July then a JUNE ship cannot be that crop —
//     it is last year's, out of the bin. At 6, a wheat delivery starting in
//     June of year Y was filed as CY Y, a crop still standing in the field.
//     Kevin's call, 2026-09-03: "you can change wheat to July 1."
//
//     It moved NOTHING already stored, measured before the change rather than
//     after: across the whole book, wheat contracts with a June ship_month = 0,
//     with a June delivery_start = 0, and with a June futures symbol = 0 — the
//     last of those is structural, because CBOT wheat has no June contract
//     (H/K/N/U/Z only), so priority 3 can never produce a June for wheat at
//     all. This is a forward-looking correction to a latent hole.
//
//     Note the TICKET table below has always been 7 for wheat and is unchanged;
//     this change makes the sale table agree with it, which is the direction
//     the physical calendar requires. The BOARD table stays 7 for its own
//     separate reason and is still not interchangeable with either.
//   grainDeliveryCropYear        here (below)                   TICKET → year     7   10   10
//     "which crop was this LOAD?" — physical harvest months. A load delivered before
//     harvest belongs to LAST year's crop, whatever the board says.
//   GRAIN_PLAN_CAL[crop].harvestMo  the plan calendar           PACE clock        7   11   10
//     "how far are we from harvest?" — drives the %-sold-by-date schedule only.
//
// The board table and the contract table DISAGREE on purpose (a Sep soybean board is
// old crop; a September ship is new crop). Don't "reconcile" them.
//
const GRAIN_NEWCROP_MO = { Wheat: 7, Corn: 12, Soybeans: 11 };
const grainContractCropYear = (crop, mo, yr) => (mo >= (GRAIN_NEWCROP_MO[crop] || 12) ? yr : yr - 1);

// ── Delivery tickets (scale tickets from the elevator's Bushel export) ──
// A delivered load belongs to the crop harvested BEFORE the delivery date (wheat
// ~Jul, corn/soy ~Oct). So a corn ticket in April is last fall's crop; a wheat
// ticket in July is this year's. Used to attribute each ticket to a crop year.
const grainDeliveryCropYear = (crop, dateStr) => {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00'); if (isNaN(d.getTime())) return null;
  const mo = d.getUTCMonth() + 1, yr = d.getUTCFullYear();
  const h = { WHEAT: 7, CORN: 10, SOYBEANS: 10, OATS: 7, BARLEY: 7 }[String(crop).toUpperCase()] || 10;
  return mo >= h ? yr : yr - 1;
};
// Normalize the ticket "Commodity" to the grain_contracts crop label (title case).
// ONE RULE, TWO CALLERS — grainCropFromCommodity is the same normalizer the
// contracts importer runs, so a ticket and the contract it fills can never
// disagree about what crop they are. This copy had drifted already: its comment
// claimed to catch PLENISH and did not (" Plenish Beans" holds no "soy" and is
// not the bare word "beans"), which is exactly the drift this file records
// happening to every rule that gets written down twice.
const grainTicketCrop = (commodity) => grainCropFromCommodity(commodity);
// Map parsed CSV rows (SheetJS json) → grain_delivery_tickets rows. Net Amount is
// the payable bushels (gross − dock); we key on (farmer_id, ticket_id) for dedup.
function parseGrainTicketRows(rows, farmerId) {
  const num = v => { const n = parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, '')); return isNaN(n) ? null : n; };
  const out = [];
  for (const r of rows || []) {
    const ticketId = String(r['Ticket ID'] ?? '').trim();
    const crop = grainTicketCrop(r['Commodity']);
    const date = String(r['Date'] ?? '').slice(0, 10);
    const net = num(r['Net Amount']);
    if (!ticketId || !crop || !date || net == null) continue;
    out.push({
      farmer_id: farmerId, ticket_id: ticketId, crop, crop_year: grainDeliveryCropYear(crop, date),
      delivery_date: date, buyer: String(r['Company Name'] ?? '').trim() || null,
      location: String(r['Location'] ?? '').trim() || null, contract_ref: String(r['Contract ID'] ?? '').trim() || null,
      net_bushels: net, gross_bushels: num(r['Gross Amount']), net_weight_lbs: num(r['Net Weight']),
      moisture: num(r['Moisture']), test_weight: num(r['Test Weight']), source: 'csv',
    });
  }
  return out;
}
async function grainImportTickets(tickets) {
  // Dedup key includes buyer so a farm delivering to MULTIPLE elevators doesn't lose
  // loads when two elevators reuse the same ticket_id (constraint: farmer_id,ticket_id,buyer).
  for (let i = 0; i < tickets.length; i += 500) {
    await sbUpsert('grain_delivery_tickets', tickets.slice(i, i + 500), 'farmer_id,ticket_id,buyer');
  }
}

// ── CORN APPRAISAL reader ────────────────────────────────────────────────────
// An appraisal is the production-to-count on acres that will never cross a
// scale — standing corn chopped for silage, or an acre destroyed by something
// the policy does not cover. Every rule lives in src/appraisalSheet.js so the
// reader, the roll-up and tools/check-appraisal-sheet.js cannot disagree.
//
// TWO FORMS, AND THEY DISAGREE ABOUT WHAT PART I MEANS. The first version of
// this prompt was written against NAU's "Corn Sorghum Appraisal", where Part I
// is the silage half — and it forced every Part I row to TON. On R&H's
// NCIS-M 215 that is wrong: Part I is "MATURE EAR CORN - POPCORN - HYBRID SEED
// - GRAIN SORGHUM AND SILAGE" and its yield column reads "(CIRCLE ONE)
// BUSHELS / TONS / POUNDS", so an ear-corn appraisal there is BUSHELS.
//
// ⚠ THE UNIT IS DECIDED BY A PEN CIRCLE. Box 7 lists all five appraisal codes
// and one is circled; every one of them is in the page's text layer whether or
// not it was picked. So the model is told to READ THE CIRCLE and to return null
// when it cannot — a guess there is the never-blend-units-of-measure failure on
// the one document where the number becomes a grower's production of record.
const SILAGE_APPRAISAL_PROMPT = `You are reading an RMA/AIP corn appraisal worksheet. Two forms exist and you must handle both:

FORM A — NCIS-M 215 "Weight Method Appraisal Worksheet" (Rain & Hail / ACE Property and Casualty)
FORM B — NAU Country "Corn Sorghum Appraisal"

Return ONLY valid JSON — no prose, no markdown.

HEADER (both forms): Company, Insured's Name, Policy No., Unit No., Claim Number, Crop, Crop Year, FSA Farm No., Appraisal Date, Remarks, and the signature dates at the foot.

APPRAISAL CODE — CRITICAL. On Form A, box 7 reads "CIRCLE APPRAISAL CODE & ENTER IN COL. 10" and lists five options:
  GRAIN SORGHUM-GS · EAR CORN-EC · POPCORN-PEC · CORN SILAGE-CS · GRAIN SORGHUM SILAGE-GSS
Exactly ONE is circled, struck through, ticked or otherwise marked. Look at the IMAGE — all five appear as text whether or not they are selected.
Return "appraisal_code" as the two- or three-letter code of the marked one ("EC", "PEC", "CS", "GS", "GSS").
If you cannot SEE which one is marked, return null. DO NOT GUESS and DO NOT default to the first one — a wrong code puts tons where bushels belong.

PART I — "MATURE EAR CORN - POPCORN - HYBRID SEED - GRAIN SORGHUM AND SILAGE WEIGHT METHOD".
Per row: Field ID, Acres in Field, Kind of Appraisal, Fraction of Acre, the sample plot weights, Total Weight All Sample Plots, No. of Sample Plots, Average Sample Weight, Yield Factor, and PER ACRE YIELD.
The per-acre yield column is labelled "(CIRCLE ONE) BUSHELS / TONS / POUNDS" — report which is circled as "yield_unit" ("BU", "TON" or "LB"), or null if not visible. Part I is NOT necessarily silage.

PART II — "MATURITY LINE WEIGHT METHOD (For ear corn from milk stage to 40% moisture)".
One field per block. Per block: Field ID, Acres in Field to tenths, and up to five STAGE rows (1/4, 1/2, 3/4, Doughy, Extended), each at a 1/100 or 1/1000 fraction of an acre, with Plot 1..Plot 9 weights, Total Weight All Sample Plots, a printed Yield Factor, and an Appraisal Per Stage.
The block foot carries "Total Appr. all Stages", "Total No. Rep. Sample Plots" and "Acre Appraisal".
Part II is always a CORN (or popcorn) per-acre figure.

Return exactly:
{
  "policy_number": string|null, "claim_number": string|null, "insured_name": string|null,
  "company": string|null, "crop": string|null, "crop_year": number|null,
  "unit_number": string|null, "fsa_farm_no": string|null, "county": string|null,
  "appraisal_code": "EC"|"PEC"|"CS"|"GS"|"GSS"|null,
  "adjuster": string|null, "appraisal_date": "YYYY-MM-DD"|null,
  "remarks": string|null,
  "fields": [
    {
      "part": "I"|"II",
      "field_id": string|null, "fsa_farm_no": string|null, "county": string|null,
      "unit_number": string|null,
      "acres": number,
      "appraised_yield": number,
      "yield_unit": "BU"|"TON"|"LB"|null,
      "appraisal_code": "EC"|"PEC"|"CS"|"GS"|"GSS"|null,
      "acre_appraisal": number|null,
      "total_all_stages": number|null,
      "total_plots": number|null,
      "stages": [
        { "stage": "1/4"|"1/2"|"3/4"|"Doughy"|"Extended",
          "fraction_denominator": 100|1000,
          "plots": [number],
          "total_weight": number|null,
          "yield_factor": number|null,
          "stage_appraisal": number|null }
      ]
    }
  ]
}

Rules:
- "appraised_yield" is the field's PER ACRE figure: Part II's "Acre Appraisal", or Part I's "Per Acre Yield".
- COPY THE ARITHMETIC AS PRINTED. Report total_weight, yield_factor, stage_appraisal, total_all_stages and total_plots exactly as they appear, even where they look inconsistent — they are used to VERIFY the reading, so silently correcting one destroys the check.
- Include a stage row ONLY where it carries a non-zero total weight. The form pre-prints every stage with 0.0; those are not appraisals.
- Include a FIELD only if it has BOTH acres and a per-acre yield.
- A blank Field ID is normal on Form A — return null, do not invent one.
- Copy "remarks" verbatim. It carries the adjuster's own words about the cause, and can be the difference between a chop appraisal and a third-party damage one.
- Strip county code prefixes ("011-Cayuga" -> "Cayuga"). Output numbers as numbers. Never invent a field, a number or a code.`;

// ── READ THE FORM, THEN ASK (Kevin, 2026-09-24: "make that work") ───────────
//
// Two parsers, and the order is the point. `src/claims/silageAppraisal.js` reads NAU's
// worksheet out of its own text — deterministic, reproducible, and checkable line by line
// against the document a grower holds. The model path handles everything it does not
// recognise, which today is every carrier but NAU: R&H's form exists and we have not seen
// one, so it cannot be the only route.
//
// ⚠ THE PARSER THAT READ IT TRAVELS WITH THE RESULT. These numbers are
// production-to-count on a federal claim; which of the two produced them is part of
// knowing what they are, and `saveSilageAppraisal` maps each shape with its own builder.
// A result carrying no `_parser` would silently take the model's path.
async function extractSilageAppraisal(file) {
  const formText = await pdfText(file);
  if (formText) {
    try {
      const doc = parseAppraisalDocument(formText);
      // A document the text parser does not recognise returns no appraisals rather than
      // throwing, so "nothing found" is the signal to fall through — not an error.
      if (doc && Array.isArray(doc.appraisals) && doc.appraisals.length) {
        return { ...doc, _parser: "text" };
      }
    } catch { /* fall through to the model — an unreadable form is not a failed upload */ }
  }
  const blocks = await buildContentBlocks(file);
  const resp = await adminApiFetch('/api/extract', {
    method: 'POST',
    body: JSON.stringify({ model: 'claude-sonnet-4-6', max_tokens: 8000,
      messages: [{ role: 'user', content: [...blocks, { type: 'text', text: SILAGE_APPRAISAL_PROMPT }] }] }),
  });
  if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.error?.message || 'HTTP ' + resp.status); }
  const data = await resp.json();
  const text = (data.content || []).map(c => c.text || '').join('');
  const m = text.match(/\{[\s\S]*\}/);
  if (!m) throw new Error('No JSON found in appraisal extraction');
  return { ...JSON.parse(m[0]), _parser: 'model' };
}

// Persist the parsed worksheet. Every rule — unit resolution, the arithmetic
// check, bushels-vs-tons — is in src/appraisalSheet.js; this only writes.
//
// ⚠ UPSERT, NEVER INSERT. `silage_appraisals` had no unique key at all until
// sql/050, so uploading the same worksheet twice DOUBLED every appraised
// bushel — on numbers that are production-to-count on a federal claim, with
// nothing on screen to say so. The conflict target must be EXPLICIT:
// `resolution=merge-duplicates` alone does not merge, it inserts.
function silageAppraisalRows(parsed, farmerId, year, fileName) {
  // ⚠ EACH PARSER HAS ITS OWN ROW BUILDER, AND THE SHAPES ARE NOT INTERCHANGEABLE. The
  // text parser emits `parseAppraisalDocument`'s shape and maps it with `toStorageRows`;
  // the model emits the prompt's shape and maps it with `appraisalRows`. Running one
  // through the other's builder yields rows of nulls that pass every filter below.
  const viaText = parsed && parsed._parser === 'text';
  return (viaText
    ? toStorageRows(parsed, { farmerId, sourceFile: fileName || null }).rows
    : appraisalRows(parsed, {
        farmerId,
        cropYear: Number(parsed && parsed.crop_year) || year,
        sourceFile: fileName || null,
      })
  ).filter(r => r.appraised_acres > 0 && r.appraised_qty != null);
}
// `prov` = { sha256, storagePath } — the stored original. Written onto every row so a figure
// can always be traced to the PDF it came from (sql/099). Before that migration the columns
// do not exist, and the write falls back to the old shape rather than failing the upload.
async function saveSilageAppraisal(parsed, farmerId, year, fileName, prov) {
  const base = silageAppraisalRows(parsed, farmerId, year, fileName);
  const rows = prov ? withProvenance(base, prov.sha256, prov.storagePath) : base;
  if (rows.length) {
    try {
      await sbUpsert('silage_appraisals', rows, 'farmer_id,crop_year,source_file,line_no');
    } catch (e) {
      if (!prov || !isMissingProvenanceColumn(e && e.message)) throw e;
      await sbUpsert('silage_appraisals', withoutProvenance(rows), 'farmer_id,crop_year,source_file,line_no');
    }
  }
  return rows;
}

// SHA-256 of the uploaded bytes. The document's identity is its CONTENT — the file name is
// whatever the person saving it typed, and the same worksheet under two names is the
// double-count this exists to stop.
async function fileSha256(file) {
  const buf = await file.arrayBuffer();
  const dig = await window.crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(dig)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── DOES THIS WORKSHEET BELONG TO THE FARM IT IS BEING FILED AGAINST? ────────
//
// The upload is scoped to whichever farm is on screen, and the document names its own
// policy. Those are two different facts, and when they disagree the appraisal is being
// attached to a grower who did not grow it — production counted against the wrong farm's
// claim, which is the worst thing this table can hold.
//
// ⚠ IT REFUSES ONLY ON A PROVEN MISMATCH. A form that names no policy, or a farm whose
// policies this screen could not read, is NOT evidence of a mismatch — refusing there
// would block a correct upload on our own missing data. Same rule the payment attachment
// follows: prove it wrong before rejecting it.
async function appraisalPolicyMismatch(parsed, farmerId, year) {
  const stated = String((parsed && (parsed.policyNumber || parsed.policy_number)) || '').trim();
  if (!stated) return null;
  const pols = await sbFetch('policies', {
    filters: [`farmer_id=eq.${farmerId}`, `crop_year=eq.${year}`], select: 'policy_number',
  }).catch(() => null);
  if (!pols || !pols.length) return null;
  // Compare on digits: a form prints NY-944-1014132-26 and the book may hold it with or
  // without its punctuation, and a hyphen is not a difference of insured.
  const digits = (s) => String(s || '').replace(/\D+/g, '');
  const want = digits(stated);
  if (!want) return null;
  const mine = pols.map(p => digits(p.policy_number)).filter(Boolean);
  if (mine.some(d => d === want || d.includes(want) || want.includes(d))) return null;
  return `That worksheet names policy ${stated}, which is not on this farm for ${year}. `
       + `Open the grower it belongs to and upload it there.`;
}

// Insured bushel guarantee for a grain crop from raw crop_coverage rows (base MPCI
// only — SCO/ECO/ARPI top-ups carry no base acres). Returns null when nothing is
// keyed, so a not-yet-covered crop year reads BLANK instead of a stale prior number.
function grainGuaranteeFromCoverage(rows, grainCrop, year) {
  const b = grainBushelsFromCoverage(rows, grainCrop, year);
  return b ? b.guaranteeBu : null;
}
// Both bases from raw crop_coverage rows: INSURED guarantee and EXPECTED production
// (no coverage haircut). Base MPCI only — SCO/ECO/ARPI top-ups carry no base acres.
//
// THE GUARANTEE IS THE PRE-HARVEST SELL CEILING, so it is computed PER COVERAGE LINE
// and liability-first, exactly the way the farmer card and the Claim Manager do it:
//
//   • liability ÷ projected price  = RMA's own acre-weighted Marketable Guarantee,
//     the figure the SOI prints. Used whenever the line carries liability.
//   • APH × coverage × acres only for a line RMA hasn't keyed liability on yet.
//
// Both go through the shared `guaranteeBushels()` so this can never drift from the
// card again. What this REPLACED (and why it matters): the grain tool used to blend
// the whole crop — sum acres, acre-weight the APH, then apply `Math.max()` of every
// county's coverage level. That overstates a farm insured 75% in one county and 80%
// in another by 5 points on the 75% acres, and 5 points of guarantee is 5 points of
// "safe to forward-sell". Meisenzahl's 2026 beans read 16,975 bu that way against
// ~15,400 by RMA's own liability, while 27,000 bu were already sold.
//
// `incomplete` is the other half of the contract: a line that produced NO bushels, or
// one carrying liability with NULL acres (so the acre total under-reads), is REPORTED,
// never silently dropped. A short denominator makes % sold read high — the same
// direction as over-selling — so it has to be visible. See the grain card's coverage
// note and [[feedback_no_guessing]].
//
// ── AND PREVENT PLANT COMES OUT FIRST (fixed 2026-08-16) ─────────────────────
// `crop_coverage.acres` is INSURED acres, and insured = planted + prevent plant.
// This read it raw, so every PP acre was counted as ground that grows bushels.
// Kevin caught it on the Crop Plan: Sullivan's 2026 corn read 930.92 ac against
// the farmer card's 821.6, and the gap is exactly his 109.37 PP acres — soybeans
// 889.48 vs 776.20, exactly 113.28 PP. At the APH that is 18,265 phantom bushels
// of corn and 5,777 of soybeans on ONE farm.
//
// It fails in the one direction that costs money: a bigger denominator makes
// "unsold" bigger and "% sold" smaller, so the tool invites a grower to forward-
// sell bushels that cannot exist — nothing is planted on prevent-plant ground.
// Same shape as the coverage-blending bug above, and the same phrase applies: the
// guarantee is the pre-harvest sell ceiling.
//
// Routed through `plantedSplit()`, the ONE entry point the farmer card, the county
// headers, both footers and the PDF already use — NOT a second split rule living
// here, which is how the card and the tool would drift apart again. Pass
// `unitsByKey` and it sums RMA's own per-unit rows; omit it and it falls back to
// the PP-factor model, which is the documented fallback and still far closer than
// counting PP as planted. `check-shared-rules` holds the arity at 3.
// ⚠ THIS FILE HAS ITS OWN COPY OF THE RESOLVER, AND PATCHING ONLY THE MODULE
// LEFT THE FEATURE INERT. src/positions/grainRules.js is a byte-sourced
// duplicate of this cluster (App.jsx exports nothing, so buildGrainPosition
// imports the copy). The organic split was added to the module first and the
// page did not move: cropInsurance() — everything the Grain Marketing screen
// renders — calls THIS function, so the panel showed W Cleveland's corn as one
// blended 389 ac / 159.8 bu row and offered no organic position at all.
// Both copies carry the split now. check-grain-rules is what keeps them equal.
//
// ── ORGANIC IS ITS OWN MARKETING CROP ─────────────────────────────────────
// grainCropParts and isOrganicRow are IMPORTED from src/positions/grainRules
// rather than restated here. App.jsx already imports a DIFFERENT
// isOrganicPractice from mpciWheat2027 (wheat practice codes), so a local
// copy is both a name collision and the duplicate-rule trap this file keeps
// recording.
// RMA rates AND PRICES organic separately — CY2026 wheat is $5.76 conventional
// against $11.16 organic — so an acre-weighted blend is a guarantee true of
// neither field. W CLEVELAND FARMS CY2026 corn is 275.5 organic acres beside
// 239.1 conventional; blended, neither number is his.
//
// The practice travels inside the CROP NAME ("ORGANIC CORN"), so every existing
// caller inherits the split without a new argument.
//
// ⚠ TRANSITIONAL (714) COUNTS AS ORGANIC HERE AND THAT IS A CHOICE. RMA rates
// and prices it on the organic tables, which is what decides a guarantee, so
// for a MARKETING position it belongs with organic. isCertifiedOrganicPractice
// is kept separate for any surface that needs the agronomic distinction.
function grainBushelsFromCoverage(rows, grainCrop, year = null, unitsByKey = null) {
  // The marketing crop may carry the practice ('ORGANIC CORN'); the insurance
  // crop underneath is still CORN.
  const parts = grainCropParts(grainCrop);
  const target = parts.base;
  const isMatch = c => { const u = String(c || '').toUpperCase(); return target === 'WHEAT' ? (u === 'WHEAT' || u === 'WINTER WHEAT') : u === target; };
  // A CONVENTIONAL position must EXCLUDE organic rows, not merely fail to ask
  // for them — otherwise splitting the book leaves the organic acres counted
  // twice, once on their own row and once inside conventional.
  const practiceOk = r => isOrganicRow(r) === parts.organic;
  const isTopUp = p => /ECO|SCO|ARPI/i.test(String(p || ''));
  // Corn insures as GRAIN (GSG, bu/ac) or SILAGE (GSS/026, ton/ac). Grain marketing
  // is bushels — never fold a tonnage line into a bushel guarantee (units invariant).
  const isSilage = r => { const t = String(r.type || '').toUpperCase(); return t === 'GSS' || t === '026'; };
  const fallbackPx = year != null ? (RMA_SPRING_PRICES[year]?.[target] || 0) : 0;
  let guar = 0, exp = 0, acres = 0, covNum = 0, covDen = 0, aphNum = 0, aphDen = 0, liability = 0, any = false;
  let ppAcresTot = 0, ppSeen = false, ppExact = false;
  const incomplete = [];
  (rows || []).filter(r => isMatch(r.crop) && practiceOk(r) && !isTopUp(r.plan) && !isSilage(r)).forEach(r => {
    const aph = Number(r.aph_yield) || 0, cov = Number(r.coverage_level) || 0;
    const pe = Number(r.price_election) || 0;
    const px = pe > 0 ? pe : fallbackPx;
    // PREVENT PLANT OFF THE TOP, through the shared rule. `pp_acres` NULL means NOT
    // DETERMINED, never zero — but a row that never selected the column is also
    // null, and those are not the same thing. Flagged below rather than guessed at,
    // because silently treating "unknown" as "none" is how PP got counted as
    // planted in the first place.
    const ppAc = r.pp_acres != null ? Number(r.pp_acres) || 0 : 0;
    const split = plantedSplit(r, ppAc, unitsByKey);
    if (ppAc > 0 || (split.ppAcres || 0) > 0) { ppSeen = true; ppAcresTot += split.ppAcres || ppAc; if (split.exact) ppExact = true; }
    const ac  = split.acres != null ? Number(split.acres) : 0;
    const lib = split.liability != null ? Number(split.liability) : 0;
    // RMA STATES this per unit (#120 x acres); prefer it over anything derived.
    // Same lookup plantedSplit() just used, so the two cannot disagree about
    // which units belong to this row.
    const uRows  = unitsByKey
      ? unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode ?? r.type_code, r.practiceCode ?? r.practice_code)]
      : null;
    const stated = statedGuaranteeBu(uRows);
    const uGuar = guaranteeBushels(lib > 0 ? lib : null, px, aph, cov, ac, (r.priceElectionPct ?? r.price_election_pct), stated);
    if (uGuar == null) { incomplete.push({ county: r.county || '—', why: 'no liability and no APH/acres' }); return; }
    any = true;
    guar += uGuar;
    // RMA's own dollar liability — the REVENUE guarantee, straight from the file.
    // Never derived: this is what the policy pays out to, and it's what the
    // profitability floor is measured against. PLANTED liability: the PP units carry
    // their own reduced guarantee and no crop, so they buy nothing to sell.
    if (lib > 0) liability += lib;
    // Expected = the same guarantee with the coverage haircut removed. Off liability
    // that means ÷ cov; off APH×cov×acres it is just APH×acres. Either way the two
    // bases stay consistent with each other, which the % sold toggle depends on.
    exp += cov > 0 ? uGuar / cov : uGuar;
    if (ac > 0) { acres += ac; if (cov > 0) { covNum += cov * ac; covDen += ac; } if (aph > 0) { aphNum += aph * ac; aphDen += ac; } }
    else incomplete.push({ county: r.county || '—', why: 'liability keyed but acres not reported' });
  });
  if (!any) return null;
  // A caller that did not SELECT pp_acres cannot tell "no prevent plant" from "never
  // looked", and the difference is thousands of phantom bushels. Say which it is.
  const ppUnknown = (rows || []).some(r => isMatch(r.crop) && !isTopUp(r.plan) && !isSilage(r)
                                           && !('pp_acres' in r));
  if (ppUnknown) incomplete.push({ county: '—', why: 'prevent-plant acres not loaded — any PP is still counted as planted' });
  return {
    guaranteeBu: guar || null,
    expectedBu: exp || null,
    acres: acres || null,
    liability: liability || null,
    // ACRE-WEIGHTED, never Math.max — display figures only; the guarantee above is
    // already summed per line and does not go through these.
    coverage: covDen > 0 ? covNum / covDen : null,
    aph: aphDen > 0 ? aphNum / aphDen : null,
    // What came OUT, so the acres figure can be reconciled to an SOI rather than
    // just quietly differing from it. `ppExact` says RMA's own per-unit rows carried
    // the split; false means it was modelled at the PP factor.
    ppAcres: ppSeen ? +ppAcresTot.toFixed(2) : 0,
    ppExact,
    incomplete,
  };
}

// grainChopAdjust MOVED to src/positions/grainRules.js — it has two callers now
// (this file's cropInsurance, and buildGrainPosition for the book view), and a
// rule with two callers does not get two copies. Imported at the top of this file.

// CARRY (store-vs-sell) net of the REAL cost of carry. The forward curve pays a
// "market carry" to hold a deferred contract vs the nearby, but storing isn't free:
//   net carry = (deferred futures − nearby futures)              ← market pays you
//             − storage (¢/bu/mo × months)                        ← physical cost (on-farm vs elevator)
//             − interest (grain value × annual rate × months/12)  ← opportunity cost of not cashing
// Positive net → the market pays you to store; price the deferred month, not the
// nearby. Negative/inverse → storing loses after costs; price the nearby.
// storagePerMo + interestRate are DECIMALS ($/bu/mo e.g. 0.025; and e.g. 0.08).
function grainCarryLadder(curve, { storagePerMo = 0.025, interestRate = 0.08 } = {}) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const fwd = (curve || []).filter(c => c && c.settle_price != null && c.contract_date && c.contract_date > todayISO)
    .sort((a, b) => (a.contract_date < b.contract_date ? -1 : 1));
  if (fwd.length < 2) return null;
  const mBetween = (a, b) => { const [ay, am] = a.split('-').map(Number), [by, bm] = b.split('-').map(Number); return (by - ay) * 12 + (bm - am); };
  const near = fwd[0], nearP = +near.settle_price;
  const rungs = fwd.slice(1).map(c => {
    const m = mBetween(near.contract_date, c.contract_date);
    if (m <= 0) return null;
    const gross = +c.settle_price - nearP;            // market carry the curve pays
    const storage = storagePerMo * m;                 // physical storage
    const interest = nearP * interestRate * (m / 12); // opportunity cost of the tied-up value
    return { month: c.contract_date, m, gross, storage, interest, cost: storage + interest, net: gross - (storagePerMo * m) - (nearP * interestRate * (m / 12)) };
  }).filter(Boolean);
  if (!rungs.length) return null;
  const best = rungs.reduce((a, b) => (b.net > a.net ? b : a), rungs[0]);   // horizon with the most net carry
  return { near: near.contract_date, nearP, rungs, best };
}

// A slim delivered-vs-contracted progress bar, reused across the grain tool. Fills
// to `pct` (0–100, clamped); pass over=true to tint the fill amber when the crop is
// over-delivered (delivered beyond contract = open storage).
function GrainProgressBar({ pct, height = 6, color = '#2d6a2d', track = '#eef3ec', over = false, radius = 4 }) {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ position: 'relative', width: '100%', height, background: track, borderRadius: radius, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, width: clamped + '%', background: over ? '#e0a52b' : color, borderRadius: radius, transition: 'width .3s' }} />
    </div>
  );
}

// Portfolio view of every OPEN marketing position (crop × crop-year) for a farm —
// so the whole book is visible at a glance, then click a card to drill into its plan.
function GrainPositionsOverview({ positions, selected, onSelect, isMobile = false }) {
  if (!positions || !positions.length) return null;
  const fmtBu = v => v == null ? '—' : Math.round(v).toLocaleString();
  const stageLabel = (mth, delivered) => {
    if (mth == null) return '';
    if (mth >= 6) return 'forward · pre-harvest';
    if (mth >= 1) return 'pre-harvest';
    if (mth === 0) return 'at harvest';
    if (mth >= -4) return delivered > 0 ? 'delivering' : 'marketing';
    return 'stored · tail';
  };
  const statusOf = (p) => {
    // Pace status (behind / ahead / on pace) removed per request — keep only the
    // non-pace descriptors: a fully-priced position, or an early forward new crop.
    if (p.pricedPct != null && p.pricedPct >= 100) return { txt: '✓ done', bg: '#e6f4e0', fg: '#2d6a2d' };
    if (p.forward && p.pricedBu <= 0 && (p.mth == null || p.mth > 3)) return { txt: 'forward', bg: '#eef2ee', fg: '#60735f' };
    return null;
  };
  const cropName = positions[0] ? positions[0].crop : '';
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 8 }}>{cropName} positions · by crop year</div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(215px, 1fr))', gap: 10 }}>
        {positions.map(p => {
          const color = GRAIN_COLORS[p.crop] || '#2d4a2d';
          const st = statusOf(p);
          const sel = selected && selected.crop === p.crop && selected.year === p.year;
          const pct = p.pricedPct == null ? 0 : p.pricedPct;
          return (
            <div key={p.crop + p.year} onClick={() => onSelect(p.crop, p.year)} title={`Open ${p.year} ${p.crop} plan`} style={{
              cursor: 'pointer', background: sel ? '#fbfefb' : '#fff', borderRadius: 10, padding: '11px 13px',
              border: `1px solid ${sel ? color : '#e4ece4'}`, borderLeft: `4px solid ${color}`,
              boxShadow: sel ? `0 1px 0 ${color}, 0 0 0 1px ${color}22` : '0 1px 2px rgba(20,40,20,0.04)', transition: 'border-color .1s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#1a2e1a' }}>{p.year} {p.crop}</span>
                {st && <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 20, background: st.bg, color: st.fg, whiteSpace: 'nowrap' }}>{st.txt}</span>}
              </div>
              <div style={{ fontSize: 10, color: '#677167', marginTop: 1 }}>{stageLabel(p.mth, p.deliveredBu)}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
                <span style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{p.pricedPct == null ? '—' : p.pricedPct + '%'}</span>
                <span style={{ fontSize: 10, color: '#677167' }}>priced</span>
              </div>
              <div style={{ position: 'relative', height: 7, borderRadius: 4, background: '#eef3ee', marginTop: 5 }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, pct)}%`, borderRadius: 4, background: color }} />
              </div>
              <div style={{ fontSize: 10, color: '#60735f', marginTop: 6 }}>{p.basisBu ? <>{fmtBu(p.pricedBu)} / {fmtBu(p.basisBu)} bu</> : <span style={{ color: '#b8860b' }}>coverage not keyed yet</span>}{p.deliveredBu > 0 ? ` · del ${fmtBu(p.deliveredBu)}` : ''}{p.openStorageBu > 0 ? <span style={{ color: '#a06a00', fontWeight: 700 }}> · OS {fmtBu(p.openStorageBu)}</span> : ''}</div>
              {/* Committed past the insured guarantee while the crop is still growing —
                  the one exposure the tool exists to catch, so it rides on the card. */}
              {p.overGuarBu > 0 && (
                <div style={{ fontSize: 10, fontWeight: 700, color: '#c0392b', marginTop: 4, lineHeight: 1.35 }}>
                  ⚠ {fmtBu(p.overGuarBu)} bu committed past the guarantee
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── GRAIN TARGET ALERTS ──────────────────────────────────────────────────────
// A target level is only worth computing if somebody is told when it prints. These
// ride on the EXISTING `price_alerts` table (alert_type='grain_target') so they
// inherit its owner RLS, its 24h cooldown, its farmer link and the shared
// fire/email engine in api/rma-lrp-sync.js — no second alert system.
//
// The row is deliberately the rung, not a hand-typed number: commodity = crop,
// contract_sym/contract_date = the board the order rests on, threshold_value = the
// level, bushels = what the rung covers. So "rest $6.75 on WN27" and the alert that
// fires are the same object, and the alert can't drift from the plan that made it.
// ── PAUSED 2026-08-12 (Kevin) — WE CANNOT TELL WHEN A TARGET ACTUALLY HIT ────
// The plumbing below works and is left intact; what's missing is the SIGNAL.
// A resting sell order fills INTRADAY. Our only grain price feed is USDA's daily
// grain PDF, which publishes one SETTLEMENT per contract per day — no high, no
// low, no intraday tick (verified: 39 of 504 rows in futures_contract_history
// carry a `high`, all of them from the one-time Yahoo seed). So settle-only gets
// it wrong in BOTH directions:
//   • market rallies through $6.80, the order fills, market settles $6.65
//     → the farmer's order FILLED and we never say a word.
//   • market settles at the level having touched it hours earlier
//     → we email "your target hit" about a move that's already over.
// Telling a grower his target hit, on a number that neither confirms nor denies
// a fill, is worse than saying nothing. Flip this back on only when we can see
// the session's HIGH (or the fill itself):
//   1. USDA MyMarketNews API (marsapi.ams.usda.gov) — free, same USDA umbrella as
//      _mars.js, try the existing MARS key first; needs checking for daily OHLC.
//   2. the elevator's own fill confirmation (Bushel/Howlett) — the only true
//      fill signal, but it's after the fact.
//   3. a paid intraday feed (Barchart / CME DataMine) — see the standing note on
//      live pricing being deferred until there's a data budget.
// Do NOT re-enable by inferring a high from open/close. That is exactly the kind
// of plausible-but-unsourced number rule zero exists to stop.
const GRAIN_TARGET_ALERTS_ENABLED = false;
const GRAIN_ALERT_TYPE = 'grain_target';
async function grainLoadTargetAlerts(crop = null) {
  const q = new URLSearchParams({ select: '*', alert_type: `eq.${GRAIN_ALERT_TYPE}`, order: 'threshold_value.asc' });
  if (crop) q.set('commodity', `eq.${crop}`);
  const r = await fetch(`${SUPABASE_URL}/rest/v1/price_alerts?${q}`, { headers: getAuthHeaders() });
  return r.ok ? await r.json() : [];
}
async function grainSaveTargetAlert(row) {
  const uid = getCurrentUserId();
  if (!uid) throw new Error('Your session expired — sign out and back in to set a target.');
  // on_conflict must name the unique index's columns EXACTLY — `price_alerts_target_key`
  // (user_id, alert_type, commodity, contract_sym, threshold_value, comparison_op).
  // Plain columns, non-partial, on purpose: PostgREST can't infer an expression or
  // partial index, so a coalesce()/WHERE version throws 42P10 instead of merging.
  // With this in place, re-seeding a rung updates it rather than stacking a
  // duplicate — which is what makes "Alert me on all" safe to press twice.
  const r = await fetch(`${SUPABASE_URL}/rest/v1/price_alerts?on_conflict=user_id,alert_type,commodity,contract_sym,threshold_value,comparison_op`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), Prefer: 'resolution=merge-duplicates,return=representation' },
    body: JSON.stringify([{ ...row, user_id: uid, alert_type: GRAIN_ALERT_TYPE, active: true }]),
  });
  if (!r.ok) throw new Error(await r.text());
  return (await r.json())[0];
}
async function grainDeleteTargetAlert(id) {
  await fetch(`${SUPABASE_URL}/rest/v1/price_alerts?id=eq.${id}`, { method: 'DELETE', headers: { ...getAuthHeaders(), Prefer: 'return=minimal' } });
}
async function grainToggleTargetAlert(a) {
  await fetch(`${SUPABASE_URL}/rest/v1/price_alerts?id=eq.${a.id}`, {
    method: 'PATCH', headers: { ...getAuthHeaders(), Prefer: 'return=minimal' }, body: JSON.stringify({ active: !a.active }),
  });
}

// May the EFFECTIVE viewer see Howlett's posted bids? True for an admin (Kevin /
// Mike) or while the Cash Board switch is on (app_config.cash_board_enabled).
// Resolved once per page load — entering or leaving Preview-as hard-reloads,
// so a cached answer cannot outlive the identity it was computed for. Fails
// CLOSED: a failed read means hidden, because the switch exists for privacy.
let _howlettBidsVisibleP = null;
function howlettBidsVisibleP() {
  if (isCurrentUserAdmin()) return Promise.resolve(true);
  if (!_howlettBidsVisibleP) {
    _howlettBidsVisibleP = sbFetch('app_config', { select: 'cash_board_enabled', filters: ['id=eq.1'], fresh: true })
      .then(r => !!(r && r[0] && r[0].cash_board_enabled === true))
      .catch(() => false);
  }
  return _howlettBidsVisibleP;
}
function GrainTargetPlanner({ isMobile = false, generic = false, farm = null, dataYear = null, onCropYear = null, onSaveCrop = null, onImportTickets = null, ticketImport = null, crop: cropProp = null, onCropChange = null, hideCropTabs = false }) {
  // generic = the Market Intelligence read-only view: price + sliders + target
  // levels only. Hides the farmer "should I price today?" card (with the % input)
  // and the discipline schedule — those are the personal marketing-plan pieces.
  // farm = { name, byCrop: { Wheat|Corn|Soybeans: { guaranteeBu, pricedBu } } }.
  // When present, "% priced" auto-fills from booked sales ÷ insured-guarantee
  // bushels, and the targets show real bushel amounts. Manual entry overrides,
  // kept per-crop so switching crops doesn't carry a stale override.
  const [cropInternal, setCropInternal] = useState('Wheat');
  const crop = cropProp != null ? cropProp : cropInternal;         // controllable so the positions overview can drill in
  const setCrop = onCropChange || setCropInternal;
  const [chist, setChist] = useState(null);   // per-contract daily history (futures_contract_history) for the crop
  const [cont, setCont] = useState(null);      // continuous nearby history (futures_history_daily) for market context
  const [curve, setCurve] = useState([]);      // latest forward curve (futures_settles_daily) — for carry
  const [contractSym, setContractSym] = useState(null);
  const [pricedByCrop, setPricedByCrop] = useState({});   // per-crop MANUAL % override; overrides the farm auto-fill
  const [storageCentsMo, setStorageCentsMo] = useState('');  // carry: storage ¢/bu/mo override (else mode default)
  const [interestPct, setInterestPct] = useState('');        // carry: interest %/yr assumption (else 8)
  const [basisMode, setBasisMode] = useState('insured');     // % priced denominator: 'insured' guarantee | 'expected' (APH) production
  const [loading, setLoading] = useState(true);
  // ── shareable view (generic / Market Intelligence): local basis → CASH, and cost of
  // production as CONTEXT. A farmer sells cash, not futures, so a board-only target is
  // half an answer. Breakeven colors a rung; it never moves one (see src/grainTargets.js).
  const [bidRows, setBidRows] = useState(null);              // basis_calculated_daily, last 10 days
  const [elevator, setElevator] = useState(null);            // which posted board to price against
  const [shipSlot, setShipSlot] = useState(null);            // a specific posted delivery slot, else every board
  const [copOv, setCopOv] = useState({});                    // per-crop { cost, yield } overrides
  // ── Howlett's POSTED basis sheet (grain_basis_board), for the cash twin of every target ──
  // A merchandiser quotes cash, not futures. The sheet is keyed the way he actually sells:
  // term (delivered vs picked up) → location → shipping slot. NOTE this table keys soybeans
  // 'soybeans'; basis_calculated_daily keys the same crop 'soy' (see basisCommodity above).
  // Different tables, different keys — using basisCommodity here silently returns zero rows.
  const [boardBasis, setBoardBasis] = useState(null);
  // Howlett's posted bids are withheld from non-admins while the Cash Board switch
  // is off. RLS already returns no Howlett rows to such a session (sql/089); this
  // is the second guard, so a Howlett option can never reach the "Cash at" picker
  // on a portal surface even if a policy over-returns. null = not known yet, and
  // not known is treated as hidden.
  const [howlettOk, setHowlettOk] = useState(() => (isCurrentUserAdmin() ? true : null));
  useEffect(() => {
    let alive = true;
    howlettBidsVisibleP().then(v => { if (alive) setHowlettOk(v); });
    return () => { alive = false; };
  }, []);
  const commodity = GRAIN_CROP_TO_COMMODITY[crop];
  // The basis table keys soybeans as 'soy' — NOT 'soybeans'. Getting this wrong silently
  // yields zero bids and the cash column just vanishes.
  const basisCommodity = crop === 'Soybeans' ? 'soy' : commodity;
  useEffect(() => {
    let alive = true; setLoading(true); setChist(null); setCont(null); setCurve([]); setContractSym(null);
    const run = (attempt) => Promise.all([
      sbFetch('futures_contract_history', { filters: [`commodity=eq.${commodity}`], select: 'contract_sym,contract_date,bar_date,close,high', order: 'bar_date.asc' }).catch(() => []),
      sbFetch('futures_history_daily', { filters: [`commodity=eq.${commodity}`], select: 'bar_date,close,high', order: 'bar_date.asc' }).catch(() => []),
      sbFetch('futures_settles_daily', { filters: [`commodity=eq.${commodity}`, 'contract_date=not.is.null'], select: 'contract_date,settle_price,settle_date', order: 'settle_date.desc' }).catch(() => []),
    ]).then(([ch, co, fw]) => {
      if (!alive) return;
      // The positions overview fires several extra queries on farm load; if this one lost
      // the race / hit a transient error and came back empty, retry once before giving up.
      if ((!ch || !ch.length) && attempt < 2) { setTimeout(() => { if (alive) run(attempt + 1); }, 500); return; }
      setChist(ch || []); setCont(co || []);
      if (fw && fw.length) { const latest = fw[0].settle_date; setCurve(fw.filter(r => r.settle_date === latest && r.contract_date).sort((a, b) => a.contract_date < b.contract_date ? -1 : 1)); }
      setLoading(false);
    });
    run(1);
    return () => { alive = false; };
  }, [commodity]);
  // Posted local boards, so futures rungs can be shown as the CASH a farmer would actually
  // rest at his elevator. Generic view only — the personal plan has its own basis handling.
  useEffect(() => {
    if (!generic) return;
    let alive = true; setBidRows(null); setElevator(null); setShipSlot(null);   // slots are per-crop labels — never carry one across crops
    const since = new Date(Date.now() - 10 * 86400000).toISOString().slice(0, 10);
    sbFetch('basis_calculated_daily', {
      filters: [`commodity=eq.${basisCommodity}`, `bid_date=gte.${since}`],
      select: 'source,delivery_contract,delivery_basis,cash_price,bid_date', order: 'bid_date.desc',
    }).then(rows => { if (alive) setBidRows(rows || []); }).catch(() => { if (alive) setBidRows([]); });
    return () => { alive = false; };
  }, [basisCommodity, generic]);
  useEffect(() => {
    if (!generic) return;
    let alive = true; setBoardBasis(null);
    sbFetch('grain_basis_board', {
      filters: [`commodity=eq.${commodity}`],
      select: 'location,term,cme_contract,ship_month,ship_sort,basis,updated_at', order: 'ship_sort.asc',
    }).then(rows => { if (alive) setBoardBasis(rows || []); }).catch(() => { if (alive) setBoardBasis([]); });
    return () => { alive = false; };
  }, [commodity, generic]);
  // available contracts (grouped from per-contract history), sorted by delivery
  const contracts = useMemo(() => {
    const by = {};
    (chist || []).forEach(r => { (by[r.contract_sym] = by[r.contract_sym] || { sym: r.contract_sym, date: r.contract_date, rows: [] }).rows.push(r); });
    // Delivery-roll: an elevator prices off a board only until the month BEFORE its delivery month (it exits
    // ahead of first notice — never ships to Chicago). So a contract is a valid pricing reference only while
    // its delivery month is strictly AHEAD of the current month. (Wheat: Jul board active thru June, then Sep.)
    const nowd = new Date(), cy = nowd.getUTCFullYear(), cm = nowd.getUTCMonth() + 1;
    return Object.values(by).map(c => { const dt = new Date(c.date + 'T00:00:00'); return { ...c, mo: dt.getUTCMonth() + 1, yr: dt.getUTCFullYear() }; })
      .filter(c => c.yr > cy || (c.yr === cy && c.mo > cm))   // hide in-delivery / expired contracts
      .sort((a, b) => a.date < b.date ? -1 : 1);
  }, [chist]);
  // crop years available among the live boards (deferred boards keep pricing the
  // prior crop, so a year spans several boards). % sold is scoped to a crop YEAR.
  const cropYears = useMemo(() => [...new Set(contracts.map(c => grainContractCropYear(crop, c.mo, c.yr)))].sort((a, b) => a - b), [contracts, crop]);
  // In farm mode the plan is anchored to a crop year (the loaded data year); the
  // board dropdown then only shows boards that price THAT crop. Generic view = all.
  const effCropYear = (farm && dataYear != null && cropYears.includes(dataYear)) ? dataYear : null;
  const shownContracts = useMemo(
    () => effCropYear == null ? contracts : contracts.filter(c => grainContractCropYear(crop, c.mo, c.yr) === effCropYear),
    [contracts, effCropYear, crop]);
  const defContract = useMemo(() => {
    if (!shownContracts.length) return null;
    // Delivery pattern drives which board to target: "all at harvest" prices the
    // delivered / open-storage bushels off the NEAREST tradeable board (front month);
    // "spread (bins)" targets a DEFERRED board (further out, carry-relevant).
    const sm = (effCropYear != null && farm?.byCrop?.[crop]?.storageMode) || null;
    if (sm === 'deliver') return shownContracts[0];
    const hm = GRAIN_HARVEST_MO[crop]; const cut = new Date(); cut.setDate(cut.getDate() + 90); const iso = cut.toISOString().slice(0, 10);
    return shownContracts.find(c => c.mo === hm && c.date > iso) || shownContracts.find(c => c.date > iso) || shownContracts[shownContracts.length - 1];
  }, [shownContracts, crop, effCropYear, farm]);
  const contract = shownContracts.find(c => c.sym === contractSym) || defContract;
  // farm-derived guarantee + priced — ONLY when the plan is anchored to a crop year
  // (effCropYear != null) so the % sold always ties to a crop, never a board year.
  const fc = (effCropYear != null && farm && farm.byCrop) ? farm.byCrop[crop] : null;
  const guaranteeBu = fc && fc.guaranteeBu ? Number(fc.guaranteeBu) : null;             // insured guarantee (APH × cov × acres)
  const expectedBu = fc && fc.expectedBu ? Number(fc.expectedBu) : null;                // expected production (APH × acres)
  const actualBu = fc && fc.actualProduction ? Number(fc.actualProduction) : null;   // actual harvested, once known
  const cropAcres = fc && fc.acres ? Number(fc.acres) : null;
  const storageMode = fc ? (fc.storageMode || null) : null;                            // 'bins' | 'deliver' | null
  const deliveredBu = fc && fc.deliveredBu ? Number(fc.deliveredBu) : 0;                // scale-ticket total for this crop-year
  const contractedBuT = fc && fc.contractedBu ? Number(fc.contractedBu) : 0;
  const openStorageBu = fc && fc.openStorageBu ? Number(fc.openStorageBu) : 0;          // delivered − contracted (Kevin's rule)
  const ticketCount = fc ? (fc.ticketCount || 0) : 0;
  // production basis for % sold: ACTUAL once harvest is in; else insured guarantee OR
  // expected production (APH × acres), farmer-toggleable. Falls back to whichever exists.
  const preHarvestBasis = basisMode === 'expected' ? (expectedBu ?? guaranteeBu) : (guaranteeBu ?? expectedBu);
  const basisBu = actualBu != null ? actualBu : preHarvestBasis;
  const basisLabel = actualBu != null ? 'actual' : (basisMode === 'expected' ? 'expected (APH)' : 'insured');
  // Pre-harvest, never forward-price past the insured guarantee (Kevin's rule). As a % of
  // the CURRENT basis: insured basis → 100 (guarantee IS the basis); expected basis →
  // guarantee ÷ expected (coverage frac). Once ACTUAL is known (harvested), no cap.
  const preHarvestCap = (actualBu == null && guaranteeBu && basisBu) ? Math.min(100, Math.round(guaranteeBu / basisBu * 100)) : 100;
  const farmPricedBu = fc && fc.pricedBu ? Number(fc.pricedBu) : null;
  const autoPct = basisBu ? Math.min(100, Math.round((farmPricedBu || 0) / basisBu * 100)) : null;
  const manualStr = pricedByCrop[crop] != null ? pricedByCrop[crop] : '';
  const priced = manualStr !== '' && !isNaN(+manualStr) ? +manualStr : autoPct;   // manual wins, else farm auto, else null
  const tc = useMemo(() => contract ? grainContractTech(contract.rows) : null, [contract]);
  const cctx = useMemo(() => cont ? grainMarketContext(cont) : null, [cont]);
  // ⚠ THE CONTRACT-LIFE PERCENTILE CANNOT SAY ANYTHING ELSE — BUG-011, and it was on
  // the top bar of this card. futures_contract_history stores an EXPIRED contract as a
  // ~250-bar one-year segment and a LIVE one as its whole trading life (CZ26: 797 bars
  // from 2023-07-10, opening in the 2023-24 collapse), so ranking a live contract
  // against its own closes ranks it over a span its comparables never had. Measured on
  // the live board 2026-09-10, EIGHTEEN OF THE TWENTY live corn/soy/wheat contracts read
  // 96.9-99.4; the other two have 38 bars and are already withheld by PCTILE_MIN_N. A
  // badge that reads 98th for every contract, every crop, every day is unfalsifiable —
  // which is what Kevin was looking at when he called these bars wrong.
  //
  // rankContractAmongYears is the rule the Grain Summary already uses for exactly this,
  // so this is a second CALLER, not a second engine — one observation per comparable
  // contract YEAR (that year's median settle within +/-15 days of the same point before
  // delivery), today ranked among them. WZ26 on 2026-09-10: life-to-date 98.3rd, but
  // 2nd highest of 11 comparable years, behind 2022's $8.44 and just above 2021's $7.14.
  //
  // `chist` is ALREADY the whole futures_contract_history for this commodity, so no
  // second fetch: contract_date is populated on 100% of rows (56,895 measured), which
  // gives the delivery month and year without parsing a symbol.
  const contractYearRank = useMemo(() => {
    if (!contract || !tc || !chist || !chist.length) return null;
    const LET = ['', 'F', 'G', 'H', 'J', 'K', 'M', 'N', 'Q', 'U', 'V', 'X', 'Z'];
    const history = [];
    for (const r of chist) {
      const d = String(r.contract_date || '').slice(0, 10);
      if (d.length !== 10 || r.close == null || !r.bar_date) continue;
      const letter = LET[+d.slice(5, 7)];
      if (!letter) continue;
      history.push({ crop: commodity, contractMonth: letter, contractYear: +d.slice(0, 4),
        obsDate: String(r.bar_date).slice(0, 10), settle: Number(r.close) });
    }
    const letter = LET[contract.mo];
    if (!letter) return null;
    const targetOffsetDays = contractLifeOffsetDays({ contractMonth: letter, contractYear: contract.yr, obsDate: tc.asOf });
    return rankContractAmongYears({ crop: commodity, contractMonth: letter, targetContractYear: contract.yr,
      targetOffsetDays, targetSettle: tc.last, history });
  }, [contract, tc, chist, commodity]);
  // Pace is a CROP-YEAR concept, not a board concept. Use the crop year the plan is
  // anchored to (or, in the generic view, the crop year the selected board belongs to —
  // a deferred board still markets the prior harvested crop). The schedule then reads
  // off today's date relative to THAT crop's harvest, not months-to-the-board.
  const scheduleCropYear = effCropYear ?? (contract ? grainContractCropYear(crop, contract.mo, contract.yr) : null);
  const monthsToHarvest = grainMonthsToHarvest(crop, scheduleCropYear);
  // How far this market actually rallies — measured once off the continuous front-month
  // series, then shared by the personal plan and the board grid so both ladders agree on
  // what's reachable.
  const fillCurve = useMemo(() => grainFillCurve(cont), [cont]);
  const boardH = useMemo(() => ladderHorizon(contract && contract.date), [contract]);
  const plan = useMemo(() => (tc && boardH.days != null)
    ? grainTechTargets(tc, priced, 0, preHarvestCap, fillCurve, boardH.days) : null,
    [tc, priced, preHarvestCap, fillCurve, contract]);
  // The per-board cash twin used to live here as its own term→slot→location picker over
  // grain_basis_board, feeding only the single-contract card. It was folded into the
  // every-board grid's picker (see `cashBids` / `elevPick`) so there is ONE place a cash
  // number comes from — and so Howlett is no longer the only house you can price against.
  // ── fill odds, stated with the horizon they're measured over ──
  // The rung already carries `fill` (grainTargetLadder recomputes it for the price the rung
  // SNAPPED to, not the aim it started from). It was only ever shown on the board grid; on this
  // card the levels appeared as bare prices, which is the half of the answer that flatters them.
  // The horizon has to travel WITH the number — "60%" means nothing until you know it's 60% of
  // the days within the ~2 months this board still has to work.
  // No horizon means no odds window — not a one-month one.
  const oddsMo = boardH.days != null ? Math.max(1, Math.round(boardH.days / 21)) : null;
  const showOdds = !!(plan && plan.rungs && plan.rungs.some(r => r.fill != null));

  // ── Target alerts for this crop ────────────────────────────────────────────
  // Loaded per crop (not per board) so switching boards doesn't drop the ones you
  // already set on another month — a farm markets several contract months at once.
  const [tgtAlerts, setTgtAlerts] = useState([]);
  const [tgtBusy, setTgtBusy] = useState(null);     // level currently saving, so the row can show it
  const [tgtErr, setTgtErr] = useState(null);
  const reloadTgtAlerts = useCallback(() => {
    if (generic || !GRAIN_TARGET_ALERTS_ENABLED) return Promise.resolve();
    return grainLoadTargetAlerts(crop).then(setTgtAlerts).catch(() => setTgtAlerts([]));
  }, [crop, generic]);
  useEffect(() => { reloadTgtAlerts(); }, [reloadTgtAlerts]);
  // Match on board + level to the cent — the same key the unique index uses, so what
  // the row shows and what a re-save would collide with are the same thing.
  const alertFor = (lvl) => tgtAlerts.find(a =>
    (a.contract_sym || '') === (contract?.sym || '') && Math.abs(Number(a.threshold_value) - lvl) < 0.005);
  async function toggleTargetAlert(lvl, bushels, label) {
    setTgtErr(null); setTgtBusy(lvl);
    try {
      const existing = alertFor(lvl);
      if (existing) await grainDeleteTargetAlert(existing.id);
      else await grainSaveTargetAlert({
        commodity: crop,
        farmer_id: farm?.farmerId || null,
        contract_sym: contract?.sym || null,
        contract_date: contract?.date || null,
        crop_year: effCropYear ?? null,
        threshold_value: Math.round(lvl * 10000) / 10000,
        comparison_op: '>=',
        bushels: bushels != null ? Math.round(bushels) : null,
        notes: label ? `Plan rung — ${label}` : 'Plan rung',
      });
      await reloadTgtAlerts();
    } catch (e) { setTgtErr(String(e.message || e)); }
    setTgtBusy(null);
  }
  // "Alert me on all of these" — the point of the feature. Seeding is idempotent
  // (merge-duplicates on the rung key), so pressing it twice is a no-op, and it
  // only ADDS: an existing alert on a level is left exactly as the user left it.
  async function seedAllTargetAlerts() {
    if (!plan?.rungs?.length) return;
    setTgtErr(null); setTgtBusy('all');
    try {
      for (const r of plan.rungs) {
        if (alertFor(r.lvl)) continue;
        await grainSaveTargetAlert({
          commodity: crop,
          farmer_id: farm?.farmerId || null,
          contract_sym: contract?.sym || null,
          contract_date: contract?.date || null,
          crop_year: effCropYear ?? null,
          threshold_value: Math.round(r.lvl * 10000) / 10000,
          comparison_op: '>=',
          bushels: (r.pct != null && basisBu) ? Math.round(basisBu * r.pct / 100) : null,
          notes: `Plan rung — ${r.lbl}`,
        });
      }
      await reloadTgtAlerts();
    } catch (e) { setTgtErr(String(e.message || e)); }
    setTgtBusy(null);
  }
  // ── Every tradeable board with its 3 working orders (Market Intelligence view) ──
  // Levels come from grainTargetLadder (real technical levels, snapped to a volatility-
  // scaled horizon — see the comment on that function for the two designs this replaced).
  // The 2-yr percentile of the NEARBY is carried alongside as CONTEXT only: it says how
  // high this market already is in its own recent history, which is what tells you whether
  // the pace rule or the targets should be doing the selling. It no longer places rungs.
  const boardTargets = useMemo(() => {
    const closes = (cont || []).filter(r => r.close != null).map(r => +r.close);
    const dist = closes.slice(-504).sort((a, b) => a - b);      // ~2 yrs of nearby closes
    const nearby = closes.length ? closes[closes.length - 1] : null;
    const mktPct = (dist.length >= 100 && nearby != null) ? dist.filter(v => v < nearby).length / dist.length : null;
    return shownContracts.map(c => {
      const t = grainContractTech(c.rows);
      if (!t) return null;                                   // thin/new board — under 30 bars, no ladder
      const _lh = ladderHorizon(c.date);
      const lad = _lh.days == null ? null : grainTargetLadder(t, fillCurve, _lh.days);
      return { sym: c.sym, mo: c.mo, yr: c.yr, tc: t, mktPct, levels: lad.levels, regime: lad.regime, rsi: lad.rsi, horizonLbl: lad.horizonLbl };
    }).filter(Boolean);
  }, [shownContracts, cont, fillCurve]);
  // ── every board a farmer here can actually sell to, in ONE list ──
  // Howlett comes off the POSTED SHEET (grain_basis_board) rather than the daily snapshot of
  // it in basis_calculated_daily: same numbers, but the sheet is live the moment Kevin
  // uploads it while the snapshot waits on the next cron. Kevin's rule — "we don't need a
  // basis scrape for Howlett, it's literally in our app." Everyone else is the scraped
  // board. Shaped like a bidRows row so one code path prices them all.
  const cashBids = useMemo(() => {
    const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const howlett = (boardBasis || []).filter(r => r.basis != null && r.cme_contract && r.ship_month).map(r => ({
      source: 'howlett_' + slug(r.location), delivery_contract: r.cme_contract, delivery_period: r.ship_month,
      delivery_basis: Number(r.basis), bid_date: String(r.updated_at || '').slice(0, 10), sort: r.ship_sort,
    }));
    // Drop the snapshot's howlett_* rows so a stale slug can't shadow the live sheet.
    const all = [...howlett, ...(bidRows || []).filter(r => !/^howlett/.test(String(r.source || '')))];
    // Cash Board switch off and not an admin: no Howlett (or Plenish) board at all.
    return howlettOk === true ? all : all.filter(r => !isHowlettSource(r.source));
  }, [boardBasis, bidRows, howlettOk]);
  // Elevators posting a board for this crop — HOME FIRST (see grainElevator).
  //
  // This used to rank by how many delivery months a board quoted, "most complete first", and
  // take [0] as the default. That silently handed every cash target to WNY Energy: Medina
  // quotes nine contract months, every Howlett location quotes five. Medina is an ethanol
  // plant ~50 mi out whose corn basis runs ~20¢ over Howlett's — and ~25–30¢ of that is the
  // round trip. So the shared-with-the-farmer number was a price he'd net LESS on, off a
  // board he doesn't ship to. Breadth of quote isn't relevance; where the grain goes is.
  const elevators = useMemo(() => {
    const by = {};
    cashBids.forEach(r => {
      if (!r.delivery_contract) return;
      const e = (by[r.source] = by[r.source] || { src: r.source, set: new Set(), last: '' });
      e.set.add(r.delivery_contract);
      if (r.bid_date > e.last) e.last = r.bid_date;
    });
    // One entry per REAL bid point. Several slugs can be the same delivery point under
    // different sheet spellings; the stale one is a dead spelling still inside the ten-day
    // window, not a second elevator — so the freshest posting wins the entry.
    const best = {};
    Object.values(by).forEach(e => {
      const meta = grainElevator(e.src);
      const cur = best[meta.key];
      if (!cur || e.last > cur.last || (e.last === cur.last && e.set.size > cur.n)) best[meta.key] = { src: e.src, n: e.set.size, last: e.last, ...meta };
    });
    return Object.values(best).sort((a, b) => a.rank - b.rank || (a.last < b.last ? 1 : a.last > b.last ? -1 : 0) || b.n - a.n);
  }, [cashBids]);
  // ⚠ NO DEFAULT MARKET (Kevin, 2026-08-28, standing rule: Silas does not tell a
  // farmer where to sell). This was `elevator || elevators[0].src` — so the page
  // auto-selected a house by the repo's own "home first" rank and converted every
  // futures target to cash against it, unasked. Ranking by breadth of quote had
  // already been fixed once here for handing every target to WNY Energy; ranking
  // by anything is the same mistake, because the page has no way to know where
  // this farm actually ships. Until a human picks, the targets are FUTURES ONLY.
  const elevSrc = elevator || null;
  // House → term → delivery point. One selection underneath (the source slug); the three
  // selects are just the way a merchandiser says it — "Howlett, delivered, Avon". Each
  // hides itself when it has one option, so WNY Energy shows a single control and Howlett
  // shows all three. Changing a level keeps the best remaining match rather than clearing.
  const elevPick = useMemo(() => {
    // No `|| elevators[0]` fallback: with nothing selected this returns null and
    // the page shows futures targets only. A fallback here would re-introduce the
    // default market the line above just removed, one indirection further down.
    const cur = elevators.find(e => e.src === elevSrc) || null;
    if (!cur) return null;
    const houses = elevators.reduce((a, e) => (a.includes(e.house) ? a : a.concat(e.house)), []);
    const inHouse = elevators.filter(e => e.house === cur.house);
    const terms = inHouse.reduce((a, e) => (a.includes(e.term) ? a : a.concat(e.term)), []);
    return { cur, houses, terms, points: inHouse.filter(e => e.term === cur.term) };
  }, [elevators, elevSrc]);
  // `elevators` is rank-sorted, so the first match in a house/term IS the preferred one.
  const pickHouse = (h) => { const e = elevators.find(x => x.house === h); if (e) setElevator(e.src); };
  const pickTerm = (t) => { const e = elevators.find(x => x.house === elevPick.cur.house && x.term === t); if (e) setElevator(e.src); };
  // Latest posted basis for a board, from the chosen elevator.
  //
  // TWO traps here, both of which silently produce a WRONG cash number rather than an error:
  //
  //  1. The scrapers disagree on contract format — Howlett writes 'Z26', Deerfield/WNY write
  //     'CZ26'. Keying on the raw string drops the local elevator that matters most and the
  //     cash column just vanishes. Both are normalized to month-code + year ('Z26').
  //
  //  2. SEVERAL DELIVERY PERIODS PRICE OFF ONE CONTRACT, at DIFFERENT basis. Howlett posts
  //     Oct-Nov Z26 at −32¢ AND 26-Dec Z26 at −20¢, same bid date. Keying on the contract
  //     alone picks whichever row happens to come first — that's how the Dec '26 card showed
  //     −32¢ (the Oct-Nov bid) instead of the Dec bid. Kevin caught it. So the board's OWN
  //     delivery month picks the row: match the month name, then the year.
  const boardKey = s => String(s || '').slice(-3).toUpperCase();
  const MON3 = ['', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const CME_MO_NUM = { F: 1, G: 2, H: 3, J: 4, K: 5, M: 6, N: 7, Q: 8, U: 9, V: 10, X: 11, Z: 12 };
  const cmeOrder = (c) => { const m = boardKey(c).match(/^([FGHJKMNQUVXZ])(\d{2})$/); return m ? (2000 + +m[2]) * 100 + CME_MO_NUM[m[1]] : 9e6; };
  // ── the posted delivery slots at the chosen point ──
  // "Targets for corn delivered to Batavia in February" is a real question and it is NOT the
  // same as "targets on H27": Batavia posts 27-Jan −0.13, 27-Feb −0.13 and 27-Mar −0.08 all
  // off H27. The slot is what the farmer commits to, so it — not the board — has to be
  // selectable, and the board follows from it.
  const slotList = useMemo(() => {
    const rows = cashBids.filter(r => r.source === elevSrc && r.delivery_basis != null && r.delivery_period);
    if (!rows.length) return [];
    const latest = rows.reduce((a, b) => (a.bid_date >= b.bid_date ? a : b)).bid_date;
    const out = [];
    rows.filter(r => r.bid_date === latest).forEach(r => {
      if (!out.some(s => s.period === r.delivery_period)) out.push({ period: r.delivery_period, cme: r.delivery_contract, basis: Number(r.delivery_basis), sort: r.sort, row: r });
    });
    return out.sort((a, b) => cmeOrder(a.cme) - cmeOrder(b.cme) || (a.sort ?? 99) - (b.sort ?? 99));
  }, [cashBids, elevSrc]);
  // A slot chosen at one point may not exist at the next (Geneva posts three of wheat's
  // twelve). Rather than silently pricing a slot the point doesn't bid, fall back to auto.
  const slot = slotList.find(s => s.period === shipSlot) || null;
  const pickBasis = (sym, mo, yr) => {
    // A slot pins the basis outright — that IS the posted bid for that delivery.
    if (slot) return boardKey(slot.cme) === boardKey(sym) ? slot.row : null;
    const rows = cashBids.filter(r => r.source === elevSrc && r.delivery_basis != null
      && boardKey(r.delivery_contract) === boardKey(sym));
    if (!rows.length) return null;
    const latest = rows.reduce((a, b) => (a.bid_date >= b.bid_date ? a : b)).bid_date;   // freshest posting only
    const score = r => {
      const p = String(r.delivery_period || '').toUpperCase();
      return (p.includes(MON3[mo]) ? 2 : 0) + (p.includes(String(yr).slice(2)) ? 1 : 0);
    };
    return rows.filter(r => r.bid_date === latest).sort((a, b) => score(b) - score(a))[0];
  };
  // Pick a delivery slot and the grid becomes a quote for THAT delivery: only the board it
  // ships off, priced off its own posted basis. An elevator sells the slot, not the board —
  // and a slot the point doesn't bid would otherwise render an empty grid, so the board is
  // kept only when the slot's contract is actually tradeable today.
  const gridBoards = slot ? boardTargets.filter(b => boardKey(b.sym) === boardKey(slot.cme)) : boardTargets;
  // Cost of production — DEFAULTS from the Model Farm's own budget (its config is the one
  // place these numbers are already maintained), overridable per crop right on the card so
  // it can be pointed at whoever you're talking to. Never gates a target.
  const copDefault = GRAIN_COP_DEFAULTS[crop] || {};
  const copCost = copOv[crop]?.cost != null && copOv[crop].cost !== '' ? +copOv[crop].cost : copDefault.cost;
  const copYield = copOv[crop]?.yield != null && copOv[crop].yield !== '' ? +copOv[crop].yield : copDefault.yield;
  const breakevenBu = grainBreakeven(copCost, copYield, copDefault.byproduct || 0);
  const isHarvestMo = contract && contract.mo === GRAIN_HARVEST_MO[crop];
  const money = (v, d = 2) => v == null || isNaN(v) ? '—' : '$' + Number(v).toFixed(d);
  const buFor = (pct) => basisBu ? Math.round(pct / 100 * basisBu).toLocaleString() + ' bu' : null;
  const unpricedBu = (basisBu && priced != null) ? Math.round(Math.max(0, 100 - Math.min(100, priced)) / 100 * basisBu) : 0;
  const color = GRAIN_COLORS[crop] || '#2d4a2d';
  const MON = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const card = { background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, padding: '14px 16px', marginBottom: 14 };
  const capLbl = { fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 8 };
  const volTag = cctx && cctx.volPctile != null ? (cctx.volPctile > 0.7 ? 'elevated' : cctx.volPctile < 0.3 ? 'low' : 'normal') : null;
  const nowMo = cctx ? +cctx.asOf.slice(5, 7) : null;
  const seasNow = cctx && nowMo && cctx.seas[nowMo] != null ? cctx.seas[nowMo] : null;
  const peakSeas = cctx ? Object.entries(cctx.seas).filter(([, v]) => v != null).sort((a, b) => b[1] - a[1])[0] : null;
  // ── carry (store vs sell) net of the real cost of carry ──
  // Storage rate defaults by storage mode: on-farm bins are cheap, elevator/open
  // storage is dearer. Interest is an explicit user assumption (not a claimed live
  // rate). Both editable via the carry card inputs.
  const storageDefault = storageMode === 'deliver' ? 5 : 2.5;   // ¢/bu/mo
  const storagePerMo = (storageCentsMo !== '' && !isNaN(+storageCentsMo) ? +storageCentsMo : storageDefault) / 100;
  const interestRate = (interestPct !== '' && !isNaN(+interestPct) ? +interestPct : 8) / 100;
  const carryAnalysis = useMemo(() => grainCarryLadder(curve, { storagePerMo, interestRate }), [curve, storagePerMo, interestRate]);
  // ── composite position read (pace recommendation removed per request) ──
  const advice = (() => {
    if (!tc) return null;
    // Gentle %-sold NUDGES toward the disciplined pace — we never tell a farmer to "hold" / not sell. Waiting
    // for a higher price is a market call we can't make; the whole value of a schedule is you keep pricing
    // regardless. So the headline is always the pace + a nudge forward. Market data below = NEUTRAL context.
    // Compact data CHIPS (not sentences) — Kevin: present data cleanly, minimal text.
    const chips = [];
    chips.push({ v: grainOrd(Math.round(tc.pctile * 100)), l: 'in range', tone: tc.pctile > 0.66 ? 'hi' : tc.pctile < 0.34 ? 'lo' : 'mid' });
    if (seasNow != null) chips.push({ v: MON[nowMo], l: seasNow >= 1.02 ? 'seasonally strong' : seasNow < 0.98 ? 'seasonally soft' : 'seasonally avg', tone: seasNow >= 1.02 ? 'hi' : seasNow < 0.98 ? 'lo' : 'mid' });
    if (carryAnalysis && carryAnalysis.best && carryAnalysis.best.net > 0.02) chips.push({ v: `+${Math.round(carryAnalysis.best.net * 100)}¢`, l: 'carry beats hold cost', tone: 'hi' });
    else if (carryAnalysis && carryAnalysis.best && carryAnalysis.best.net < -0.02) chips.push({ v: 'under cost', l: 'carry vs hold cost', tone: 'mid' });
    if (plan && plan.regime === 'downtrend') chips.push({ v: 'off highs', l: 'bounce targets', tone: 'lo' });
    // Pace recommendation removed per request — neutral position headline, no
    // "behind/ahead your pace" nudge. % priced stays; market context is the chips.
    const head = priced != null ? `${priced}% priced` : 'Not yet priced';
    return { head, sub: null, suggest: null, chips, tone: 'neutral' };
  })();
  // The dot sits at where PRICE actually is in the range ((last−lo)/(hi−lo)), NOT at the
  // percentile. Those diverge: a market can close above 99% of prior days yet sit visibly
  // below its intraday high — beans closed at $12.02 (99th %ile of closes) while the range
  // top is the $12.14 intraday spike, so a percentile-positioned dot pinned it to $12.14
  // and looked wrong against the chart. Marker = value position; the number = percentile of
  // CLOSES (floored, so 99.6% shows 99, never a false "100th").
  // The dot's position and the %ile answer different questions and only agree when closes are
  // evenly spread. Wheat's 3-yr closes bunch at $5.57 with a lone $7.60 spike, so today's $6.78
  // beats 97% of closes while sitting 69% up the range — both true, and it read as a bug until
  // the bar showed the bunching. So: axis = price (matches the chart), band = where the middle
  // half of closes actually sat, tick = median. The %ile is then visibly the dot vs the band.
  // ⚠ `about` NAMES THE SERIES THE BAR IS ACTUALLY ABOUT, and `minN` is the
  // sample floor below which a percentile is withheld rather than printed.
  // Both exist because this card stacks bars from TWO different price series
  // under one contract's headline price.
  const PCTILE_MIN_N = 40;   // same floor pctileOf uses in the MI briefs
  // ── THE RANGE BAR DRAWS THE DISTRIBUTION, because the percentile IS the shaded mass.
  //
  // ⚠ THE DOT IS A PRICE AND THE NUMBER IS A RANK, and they are far apart. That is not
  // a defect and it must not be `fixed` by moving the dot: the axis is a PRICE axis, the
  // lo/hi labels and the median tick are prices, and a percentile-positioned dot once
  // pinned beans to a $12.14 intraday spike and looked wrong against the chart. What was
  // missing is any reason on screen to believe the two.
  //
  // The old bar tried to carry that with a quartile BOX and a 9.5px caption saying `when
  // closes bunch low, a price well below the range top can still beat most of them`. It
  // cannot work: between q75 and the dot the bar drew NOTHING, so the eye could not tell
  // whether that span held 5% of the days or 50%. Kevin presented it to a grower on
  // 2026-09-10 and reported the bars as visually wrong — the correct reaction to a dot
  // four-fifths along a bar labelled 96th, with a caption apologising for it.
  //
  // Measured, live front-month wheat that day: $7.2875 on a $4.95-$7.825 axis is 81.3% of
  // the width and beats 96.4% of the last 252 closes. Draw the closes and both are true
  // AND legible at once — the mass at or below today's price is shaded, so `96%` is a
  // fraction of the picture rather than a claim beside it.
  //
  // The gradient this replaces encoded NOTHING: a fixed amber-to-green ramp painted the
  // right-hand third of every bar `good` whatever the distribution did, which on wheat's
  // 3-year window is where 25% of the days live.
  const rangeBar = ({ lo, hi, last, pctile, label, q50, about = null, nObs = null, dist = null, rankNote = null, sampleNote = null }) => {
    const thin = nObs != null && nObs < PCTILE_MIN_N;
    const posOf = (v) => (hi > lo && v != null) ? Math.max(0, Math.min(1, (v - lo) / (hi - lo))) : null;
    const vpos = posOf(last) ?? 0.5, p50 = posOf(q50);
    // The right-hand text, in order of how checkable it is: a caller-supplied rank
    // (cross-year, comparable population) wins; then a percentile that names its own
    // sample; then the honest refusal.
    const readout = rankNote != null ? rankNote
      : thin ? `only ${nObs} closes so far — too few to rank`
      : `closed higher than ${Math.floor((pctile || 0) * 100)}% of ${sampleNote || 'these closes'}`;
    // Bins are coloured by whether the bin SITS AT OR BELOW today's close, so the shaded
    // area and the percentage are the same statement. The boundary is one bin wide; the
    // exact figure is printed above, and the dot marks the exact price.
    const bins = dist && Array.isArray(dist.bins) ? dist.bins : null;
    return (
      <div style={{ marginBottom: 11 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#60735f', marginBottom: 1, gap: 10 }}>
          <span>{label}{about != null ? <span style={{ color: '#b8860b', fontWeight: 700 }}> · {money(about)}</span> : null}</span>
          <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{readout}</span>
        </div>
        <div style={{ position: 'relative', height: 30 }}>
          {/* the shape of the closes, growing up from the price axis */}
          {bins ? (
            <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 18, display: 'flex', alignItems: 'flex-end' }}>
              {bins.map((v, i) => {
                const mid = lo + (hi - lo) * ((i + 0.5) / bins.length);
                const atOrBelow = last != null && mid <= last;
                return (
                  <div key={i} style={{ flex: '1 1 0', margin: '0 0.5px', height: `${Math.max(4, v * 100)}%`,
                    background: atOrBelow ? color : '#dde6dd', borderRadius: '1px 1px 0 0',
                    opacity: atOrBelow ? 0.55 : 1 }} />
                );
              })}
            </div>
          ) : (
            /* No usable shape (a thin or flat sample). Draw the axis and nothing that
               would read as a distribution — an invented shape is worse than none. */
            <div style={{ position: 'absolute', left: 0, right: 0, top: 12, height: 6, borderRadius: 3, background: '#eef3ee' }} />
          )}
          <div style={{ position: 'absolute', left: 0, right: 0, top: 18, height: 1, background: '#c8d6c8' }} />
          {p50 != null ? <div title="median close" style={{ position: 'absolute', left: `${p50 * 100}%`, top: 2, transform: 'translateX(-50%)', width: 1, height: 20, background: '#8fa08f' }} /> : null}
          <div title="today's close" style={{ position: 'absolute', left: `${vpos * 100}%`, top: 12, transform: 'translateX(-50%)', width: 12, height: 12, borderRadius: '50%', background: '#fff', border: `3px solid ${color}`, boxShadow: '0 0 0 2px rgba(255,255,255,0.85)' }} />
          <div style={{ position: 'absolute', left: 0, top: 22, fontSize: 10, color: '#677167' }}>{money(lo)}</div>
          <div style={{ position: 'absolute', right: 0, top: 22, fontSize: 10, color: '#677167' }}>{money(hi)}</div>
        </div>
      </div>
    );
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {!hideCropTabs && ['Wheat', 'Corn', 'Soybeans'].map(c => (
          <button key={c} onClick={() => setCrop(c)} style={{ padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
            border: '1px solid ' + (crop === c ? (GRAIN_COLORS[c] || '#2d4a2d') : '#d5e2d5'), background: crop === c ? (GRAIN_COLORS[c] || '#2d4a2d') : '#fff', color: crop === c ? '#fff' : '#5c6f5c' }}>{c}</button>
        ))}
        {farm && onCropYear && cropYears.length ? (
          <select value={effCropYear ?? ''} onChange={e => onCropYear(+e.target.value)} title="Crop year — % sold is scoped to this crop, not the board"
            style={{ fontSize: 13, fontWeight: 800, padding: '7px 10px', borderRadius: 8, border: '1px solid #cbe0cb', background: '#f3f9f3', color: '#1a2e1a', marginLeft: 4 }}>
            {cropYears.map(y => <option key={y} value={y}>{y} crop</option>)}
          </select>
        ) : null}
        {shownContracts.length ? (
          <select value={contract ? contract.sym : ''} onChange={e => setContractSym(e.target.value)} title="Which futures board to price against (delivery month)"
            style={{ fontSize: 13, fontWeight: 700, padding: '7px 10px', borderRadius: 8, border: '1px solid #d5e2d5', background: '#fff', marginLeft: 4 }}>
            {shownContracts.map(c => <option key={c.sym} value={c.sym}>{c.sym} — {MON[c.mo]} '{String(c.yr).slice(2)}</option>)}
          </select>
        ) : null}
      </div>
      {loading && <div style={{ padding: 40, color: '#677167' }}>Loading contract history…</div>}
      {!loading && !tc && <div style={{ padding: 16, color: '#a5461b' }}>No contract history for {crop} yet.</div>}
      {tc && plan && (<>
        {/* ── SHOULD I PRICE TODAY? — the composite, defensible read (personal plan only) ── */}
        {!generic && advice && (
          <div style={{ ...card, border: `1px solid ${advice.tone === 'go' ? '#bfe0c4' : '#dde8d8'}`, background: advice.tone === 'go' ? '#f2faf3' : '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: advice.tone === 'go' ? '#1f7a3d' : '#3a4a3a' }}>
                {advice.head}
                <span style={{ fontWeight: 600, fontSize: 12, color: '#5c6f5c' }}> · {contract ? contract.sym : ''} {crop.toLowerCase()}</span>
              </div>
              <div style={{ fontSize: 11, color: '#60735f' }}>{money(tc.last)} · {nowMo ? MON[nowMo] : ''} {cctx ? cctx.asOf.slice(0, 4) : ''}</div>
            </div>
            {advice.sub ? <div style={{ fontSize: 11.5, color: '#5c6f5c', marginTop: 2 }}>{advice.sub}</div> : null}
            {/* position vs plan */}
            {priced != null && (
              <div style={{ margin: '9px 0 3px' }}>
                <div style={{ position: 'relative', height: 12 }}>
                  <div style={{ position: 'absolute', left: 0, right: 0, top: 2, height: 8, borderRadius: 4, background: '#eef3ee' }} />
                  <div style={{ position: 'absolute', left: 0, top: 2, height: 8, borderRadius: 4, width: `${Math.min(100, priced)}%`, background: color }} />
                </div>
                <div style={{ fontSize: 10, color: '#677167', marginTop: 2 }}>{scheduleCropYear} {crop.toLowerCase()} · {monthsToHarvest == null ? '' : monthsToHarvest > 0 ? `${monthsToHarvest} mo to harvest` : monthsToHarvest < 0 ? `${-monthsToHarvest} mo past harvest — marketing stored` : 'at harvest'}</div>
              </div>
            )}
            {/* market context — compact data chips, not prose */}
            {advice.chips && advice.chips.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}>
                {advice.chips.map((c, i) => {
                  const bg = c.tone === 'hi' ? '#eaf6ec' : c.tone === 'lo' ? '#fbf1e3' : '#eef2ee';
                  const fg = c.tone === 'hi' ? '#1f7a3d' : c.tone === 'lo' ? '#b8760b' : '#5c6f5c';
                  return (
                    <div key={i} style={{ background: bg, borderRadius: 7, padding: '4px 9px', display: 'flex', alignItems: 'baseline', gap: 5 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: fg }}>{c.v}</span>
                      <span style={{ fontSize: 10.5, color: '#60735f', textTransform: 'uppercase', letterSpacing: '.04em' }}>{c.l}</span>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid #e6efe6', paddingTop: 7, fontSize: 11.5, color: '#5c6f5c' }}>
              <label style={{ fontWeight: 700 }}>% priced <input type="number" value={manualStr !== '' ? manualStr : (autoPct != null ? String(autoPct) : '')} onChange={e => setPricedByCrop(prev => ({ ...prev, [crop]: e.target.value }))} placeholder="0" style={{ width: 52, fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid #cfe0cf' }} /> % of {basisLabel}</label>
              {/* basis toggle — % priced denominator (pre-harvest only; actual overrides both) */}
              {actualBu == null && guaranteeBu && expectedBu ? (
                <span style={{ display: 'inline-flex', border: '1px solid #d5e2d5', borderRadius: 6, overflow: 'hidden' }}>
                  {[['insured', 'Insured'], ['expected', 'Expected']].map(([k, lbl]) => (
                    <button key={k} onClick={() => setBasisMode(k)} title={k === 'insured' ? 'Insured guarantee = APH × coverage × acres' : 'Expected production = APH × acres (full crop)'}
                      style={{ padding: '3px 9px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer', border: 'none', background: basisMode === k ? '#2d4a2d' : '#fff', color: basisMode === k ? '#fff' : '#60735f' }}>{lbl}</button>
                  ))}
                </span>
              ) : null}
              {basisBu ? <span style={{ fontSize: 10.5, color: '#60735f' }}>{Math.round(farmPricedBu || 0).toLocaleString()} / {Math.round(basisBu).toLocaleString()} bu {basisLabel} · {effCropYear ?? dataYear} {crop.toLowerCase()}{manualStr !== '' ? ' · manual' : ' · from contracts'}</span>
                : fc ? <span style={{ fontSize: 10.5, color: '#b8860b' }}>{effCropYear} {crop.toLowerCase()} coverage not keyed yet{farmPricedBu ? ` · ${Math.round(farmPricedBu).toLocaleString()} bu priced` : ''}</span> : null}
              <span>→ rest orders at <b>{plan.rungs.map(r => money(r.lvl)).join(' / ')}</b> on <b>{contract ? contract.sym : ''}{contract ? ` (${MON[contract.mo]} '${String(contract.yr).slice(2)})` : ''}</b> with your elevator</span>
            </div>
          </div>
        )}
        {/* harvest & storage — storage mode + actual yield → drives the production basis + open-storage advice */}
        {!generic && fc && onSaveCrop && (
          <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
            {/* Delivery plan selector removed per request */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', color: '#677167', textTransform: 'uppercase' }}>Actual yield (once harvested)</span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12, color: '#5c6f5c', flexWrap: 'wrap' }}>
                {cropAcres ? (<>
                  <input key={crop + '-y'} type="number" defaultValue={actualBu != null && cropAcres ? (actualBu / cropAcres).toFixed(1) : ''} placeholder="bu/ac"
                    onBlur={e => { const y = parseFloat(e.target.value); onSaveCrop(crop, { actual_production: (!isNaN(y) && y > 0) ? Math.round(y * cropAcres) : null }); }}
                    style={{ width: 64, fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #cfe0cf' }} /> bu/ac × {Math.round(cropAcres).toLocaleString()} ac
                  {actualBu != null ? <b style={{ color: '#1a2e1a' }}>= {Math.round(actualBu).toLocaleString()} bu</b> : null}
                </>) : (<>
                  <input key={crop + '-b'} type="number" defaultValue={actualBu ?? ''} placeholder="total bu"
                    onBlur={e => { const b = parseFloat(e.target.value); onSaveCrop(crop, { actual_production: (!isNaN(b) && b > 0) ? Math.round(b) : null }); }}
                    style={{ width: 84, fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #cfe0cf' }} /> bu total
                </>)}
                {guaranteeBu ? <span style={{ color: '#677167' }}>· insured est. {Math.round(guaranteeBu).toLocaleString()}</span> : null}
              </div>
            </div>
            {onImportTickets ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', color: '#677167', textTransform: 'uppercase' }}>Delivery tickets</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ padding: '6px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700, border: '1px solid #d5e2d5', background: '#fff', color: '#2d4a2d' }}>
                    ⬆ Import CSV
                    <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) onImportTickets(f); e.target.value = ''; }} />
                  </label>
                  {ticketImport ? <span style={{ fontSize: 10.5, color: ticketImport.status === 'err' ? '#c0392b' : ticketImport.status === 'ok' ? '#1f7a3d' : '#677167' }}>{ticketImport.msg}</span> : null}
                </div>
              </div>
            ) : null}
            {/* deliveries → contract fill + open storage (from scale tickets) */}
            {ticketCount > 0 ? (() => {
              const onContract = Math.min(deliveredBu, contractedBuT);
              const owed = Math.max(0, contractedBuT - deliveredBu);
              const denom = Math.max(deliveredBu, contractedBuT, 1);
              const pctFill = contractedBuT > 0 ? Math.min(100, Math.round(onContract / contractedBuT * 100)) : 0;
              return (
                <div style={{ flex: '1 1 100%', borderTop: '1px solid #eef3ee', paddingTop: 9, marginTop: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: '#5c6f5c', marginBottom: 4 }}>
                    <span>Delivered <b style={{ color: '#1a2e1a' }}>{Math.round(deliveredBu).toLocaleString()} bu</b> · {ticketCount} tickets · {effCropYear} {crop.toLowerCase()}</span>
                    <span>{contractedBuT > 0 ? `${pctFill}% of contracts filled` : 'no contracts booked'}</span>
                  </div>
                  <div style={{ position: 'relative', height: 9, borderRadius: 5, background: '#eef3ee', overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: `${onContract / denom * 100}%`, background: '#2f8a48' }} title="on contract" />
                    <div style={{ width: `${openStorageBu / denom * 100}%`, background: '#e0a52b' }} title="open storage" />
                    <div style={{ width: `${owed / denom * 100}%`, background: 'repeating-linear-gradient(45deg,#dfe8df,#dfe8df 3px,#eef3ee 3px,#eef3ee 6px)' }} title="still owed to contracts" />
                  </div>
                  <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 4, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    <span><span style={{ color: '#2f8a48', fontWeight: 800 }}>■</span> on contract {Math.round(onContract).toLocaleString()}</span>
                    <span><span style={{ color: '#e0a52b', fontWeight: 800 }}>■</span> open storage {Math.round(openStorageBu).toLocaleString()}</span>
                    {owed > 0 ? <span><span style={{ color: '#c0cabf', fontWeight: 800 }}>■</span> still owed {Math.round(owed).toLocaleString()}</span> : null}
                  </div>
                </div>
              );
            })() : null}
            {/* open-storage advice — real (delivered − contracted) when tickets exist, else the priced estimate */}
            {ticketCount > 0 && openStorageBu > 0 ? (
              <div style={{ flex: '1 1 100%', fontSize: 11.5, color: '#7a4a00', background: '#fbf1e3', borderRadius: 8, padding: '8px 11px' }}>
                <b>{Math.round(openStorageBu).toLocaleString()} bu</b> delivered beyond contracts — in the elevator's open storage. Priced off the nearby only; storage / DP charges accrue while it sits. Pricing (or a basis contract) stops the fees; staying open is a flat-price bet you're carrying.
              </div>
            ) : storageMode === 'deliver' && actualBu != null && unpricedBu > 0 ? (
              <div style={{ flex: '1 1 100%', fontSize: 11.5, color: '#7a4a00', background: '#fbf1e3', borderRadius: 8, padding: '8px 11px' }}>
                ≈<b>{unpricedBu.toLocaleString()} bu</b> unpriced — if delivered, it accrues open-storage / DP charges. Pricing (or a basis contract) stops the fees; staying open is a flat-price bet you're carrying.
              </div>
            ) : storageMode === 'bins' && actualBu != null && unpricedBu > 0 ? (
              <div style={{ flex: '1 1 100%', fontSize: 11, color: '#5c6f5c' }}>≈<b>{unpricedBu.toLocaleString()} bu</b> in your bins — the carry card below shows whether a deferred contract out-prices the nearby after your storage cost.</div>
            ) : null}
          </div>
        )}
        {/* context + moving averages + range bars */}
        <div style={card}>
          <div style={capLbl}>{contract ? contract.sym : '—'} — {contract ? `${MON[contract.mo]} '${String(contract.yr).slice(2)}` : ''} {crop}{contract ? (isHarvestMo ? ' · harvest delivery' : ' · deferred (stored/carry)') : ''}</div>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div><div style={{ fontSize: 26, fontWeight: 800, color }}>{money(tc.last)}</div><div style={{ fontSize: 10, color: '#677167' }}>{contract ? contract.sym : ''} · as of {tc.asOf}</div></div>
            {[['50-day', tc.ma50], ['100-day', tc.ma100], ['200-day', tc.ma200]].map(([lbl, v]) => v != null ? (
              <div key={lbl}><div style={{ fontSize: 15, fontWeight: 700, color: tc.last >= v ? '#1f7a3d' : '#b8860b' }}>{money(v)}</div><div style={{ fontSize: 10, color: '#677167' }}>{lbl} MA {tc.last >= v ? '▲' : '▼'}</div></div>
            ) : null)}
            {volTag ? <div><div style={{ fontSize: 15, fontWeight: 700, color: '#3a4a3a' }}>{volTag}</div><div style={{ fontSize: 10, color: '#677167' }}>volatility (nearby)</div></div> : null}
            {seasNow != null ? <div><div style={{ fontSize: 15, fontWeight: 700, color: seasNow >= 1.02 ? '#1f7a3d' : seasNow < 0.98 ? '#b8860b' : '#5c6f5c' }}>{seasNow >= 1.05 ? 'Peak' : seasNow >= 1.02 ? 'Strong' : seasNow >= 0.98 ? 'Average' : 'Soft'}</div><div style={{ fontSize: 10, color: '#677167' }}>{MON[nowMo]} seasonally</div></div> : null}
          </div>
          {/* ⚠ THE TOP BAR PRINTS THE CROSS-YEAR RANK, NEVER tc.pctile. The axis is
              still this contract's own closes — a legitimate price axis — but the
              number beside it is the comparable-years rank, because the life-to-date
              percentile reads 96.9-99.4 for 18 of the 20 live contracts on the board.
              Where the rank is NOT available (fewer than 3 comparable contract years
              within the window) the bar says so and prints no rank at all; it does not
              fall back to the percentile that has nothing to say. tc.pctile is
              untouched everywhere else — it still drives the target ladder's rungs,
              and that is a separate decision from what this bar displays. */}
          {rangeBar({ lo: tc.lifeLo, hi: tc.lifeHiClose != null ? tc.lifeHiClose : tc.lifeHi, last: tc.last,
            q50: tc.q50, nObs: tc.n, dist: tc.dist,
            rankNote: contractYearRank
              ? (contractYearRank.available
                  ? `${grainOrd(contractYearRank.rank)} highest of ${contractYearRank.totalYears} comparable years at this point before delivery`
                  : 'not enough comparable contract years to rank')
              : null,
            label: `${contract ? contract.sym : ''} contract-life range · ${tc.n} days` })}
          {cctx ? rangeBar({ lo: cctx.lo3y, hi: cctx.hi3y, last: cctx.last, pctile: cctx.pctile3y,
            q50: cctx.q50_3y, about: cctx.last, dist: cctx.dist3y,
            sampleNote: `the last ${cctx.dist3y ? cctx.dist3y.n : 756} sessions`,
            /* ⚠ A DIFFERENT PRICE SERIES FROM THE HEADLINE. This ranks the FRONT-MONTH
               close against front-month history; the card above is headed with the
               selected deferred contract. Naming the series and printing its price is
               what stops "$5.36 ... 99th %ile" reading as a claim about Dec-27. */
            label: `${crop} front-month, 3-yr range` }) : null}
          {cctx ? rangeBar({ lo: cctx.lo1y, hi: cctx.hi1y, last: cctx.last, pctile: cctx.pctile1y,
            q50: cctx.q50_1y, about: cctx.last, dist: cctx.dist1y,
            sampleNote: `the last ${cctx.dist1y ? cctx.dist1y.n : 252} sessions`,
            label: `${crop} front-month, 1-yr range` }) : null}
          <div style={{ fontSize: 10.5, color: '#677167', lineHeight: 1.5, marginTop: -2 }}>
            {/* ⚠ SAY IT ONCE, PLAINLY. The top bar is the SELECTED contract; the two
                below are the front month, which is a different price. */}
            The top bar is <b>{contract ? contract.sym : 'this contract'}</b> against its own history; the two below are the
            <b> front-month</b> contract against front-month history — a different price, shown beside its own bar.{' '}
            Ranges are of daily <b>closes</b>. The bars are <b>how many days closed at each price</b> — shaded where
            they are at or below today, so the percentage beside each bar is the shaded share of the picture. Dot =
            today's close, tick = the median. The top bar's number is a rank among <b>comparable contract years</b> at
            the same point before delivery, not a percentile of its own history: this archive stores an expired
            contract as one year and a live one as its whole life, so a life-to-date percentile reads in the high
            90s for nearly every live contract and cannot tell them apart.
          </div>
          {cctx ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#677167', marginBottom: 4 }}><span>Seasonal price · 10-yr avg by month</span><span>strongest ~{peakSeas ? MON[+peakSeas[0]] : '—'}</span></div>
              <div style={{ display: 'flex', gap: 2 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => {
                  const idx = cctx.seas[m]; if (idx == null) return <div key={m} style={{ flex: 1 }} />;
                  const a = Math.min(0.85, Math.abs(idx - 1) * 9 + 0.12);
                  return <div key={m} title={`${MON[m]}: ${Math.round(idx * 100)}% of the yearly average`} style={{ flex: 1, textAlign: 'center', padding: '6px 0', borderRadius: 4, fontSize: 10.5, fontWeight: 800, color: '#fff', background: idx >= 1 ? `rgba(47,138,72,${a})` : `rgba(184,134,11,${a})`, outline: m === nowMo ? '2.5px solid #1a2e1a' : 'none', outlineOffset: '-2.5px' }}>{MON[m].slice(0, 1)}</div>;
                })}
              </div>
            </div>
          ) : null}
        </div>

        {/* ── technical resting-order targets — the FARM plan only ──
            The Market Intelligence (generic) view used to render this card AND the every-board
            grid below it, which repeated the selected board's three rungs twice on one screen
            with two different cash pickers — the top one term→slot→location off Howlett's
            sheet, the bottom one a flat elevator list. Kevin: "seems redundant of targets on
            top and bottom." The grid is the superset (every board, same engine, one picker),
            so in the generic view it is now the only place targets appear. Here the card stays:
            the farm plan is scoped to ONE board on purpose (the crop year's board) and carries
            the %-priced rows the grid has no concept of. */}
        {!generic && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            <div style={capLbl}>Targets on {contract ? contract.sym : 'the contract'}{plan.mode === 'upside' ? ' — optional upside' : ''}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {/* A level nobody is watching is a level that never gets acted on —
                  but see GRAIN_TARGET_ALERTS_ENABLED: we can't yet tell when one
                  actually hit, so this stays hidden rather than promising a
                  notification we can't deliver honestly. */}
              {GRAIN_TARGET_ALERTS_ENABLED && plan.rungs?.length > 0 && plan.rungs.some(r => !alertFor(r.lvl)) && (
                <button type="button" onClick={seedAllTargetAlerts} disabled={tgtBusy === 'all'}
                  title="Email me (and the agency) when the board settles at or above these levels"
                  style={{ fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 6, border: '1px solid #c8a24a', background: '#fdf6e8', color: '#7a4a00', cursor: tgtBusy === 'all' ? 'wait' : 'pointer' }}>
                  {tgtBusy === 'all' ? 'Setting…' : '🔔 Alert me on all'}
                </button>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: plan.regime === 'downtrend' ? '#b8860b' : '#1f7a3d' }}>
                {plan.regime === 'downtrend' ? `resistance ladder · retrace → prior high` : `resistance levels · above 100-day MA`}
              </div>
            </div>
          </div>
          {tgtErr && <div style={{ fontSize: 11, color: '#c0392b', marginBottom: 6 }}>Couldn't save the alert — {tgtErr}</div>}
          {plan.mode === 'upside' && plan.already > 0 ? (
            <div style={{ fontSize: 11, color: '#5c6f5c', marginBottom: 6 }}>{plan.already}% priced. Levels to price the remaining <b>{Math.max(0, 100 - plan.already)}%</b> <b>into strength</b> if the market comes; nothing fills unless it rallies.</div>
          ) : null}
          {plan.remaining <= 0 ? (
            <div style={{ fontSize: 12, color: '#1f7a3d', fontWeight: 700, padding: '6px 8px' }}>Fully priced ({plan.already}%) — nothing left to target.</div>
          ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              <th style={{ textAlign: 'left', padding: '0 8px 4px', fontSize: 10, fontWeight: 800, color: '#677167', textTransform: 'uppercase', letterSpacing: '.05em' }}>Sell at</th>
              {showOdds ? <th style={{ textAlign: 'right', padding: '0 8px 4px', fontSize: 10, fontWeight: 800, color: '#677167', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }} title={`How often the last 5 years of front-month history rallied this far within ~${oddsMo} mo`}>Fills · ~{oddsMo} mo</th> : null}
              {/* Resistance LEVELS only — no % sizing shown (the agent advises sizing). */}
              <th style={{ padding: 0 }} />
            </tr></thead>
            <tbody>
              {plan.already > 0 ? <tr style={{ borderBottom: '1px solid #eef3ee', background: '#f6f9f5' }}>
                <td style={{ padding: '7px 8px', fontWeight: 700, color: '#60735f' }}>already priced</td>
                {showOdds ? <td /> : null}
                <td style={{ padding: '7px 8px', fontSize: 11, color: '#677167' }}>done ✓</td>
              </tr> : null}
              {plan.nowPct > 0 ? <tr style={{ borderBottom: '1px solid #eef3ee' }}>
                <td style={{ padding: '7px 8px', fontWeight: 800, color }}>{money(tc.last)} <span style={{ fontSize: 10, fontWeight: 600, color: '#677167' }}>at market</span></td>
                {/* no fill odds on the market price — it's already there, nothing to rally to */}
                {showOdds ? <td style={{ padding: '7px 8px', textAlign: 'right', fontSize: 11, color: '#c8d3c8' }}>—</td> : null}
                <td style={{ padding: '7px 8px', fontSize: 11, color: '#5c6f5c' }}>{grainOrd(Math.round(tc.pctile * 100))} %ile</td>
              </tr> : null}
              {plan.rungs.map((r, i) => {
                const al = alertFor(r.lvl);
                const rungBu = (r.pct != null && basisBu) ? Math.round(basisBu * r.pct / 100) : null;
                return (
                <tr key={i} style={{ borderBottom: '1px solid #eef3ee' }}>
                  <td style={{ padding: '7px 8px', fontWeight: 800, color: '#1a2e1a' }}>{money(r.lvl)} <span style={{ fontSize: 10, fontWeight: 600, color: '#677167' }}>reference level</span></td>
                  {showOdds ? <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap',
                    color: r.fill == null ? '#c8d3c8' : r.fill >= 0.5 ? '#1f7a3d' : r.fill >= 0.3 ? '#5c6f5c' : '#b8860b' }}>
                    {r.fill != null ? `${Math.round(r.fill * 100)}%` : '—'}
                  </td> : null}
                  <td style={{ padding: '7px 8px', fontSize: 11, color: '#5c6f5c' }}>
                    at the <b>{r.lbl}</b>
                    {/* The bell IS the level — one click turns a computed rung into a
                        watched one. Set = amber and filled, so a glance says which
                        levels are being watched and which are just drawn. */}
                    {GRAIN_TARGET_ALERTS_ENABLED && (
                    <button type="button" onClick={() => toggleTargetAlert(r.lvl, rungBu, r.lbl)} disabled={tgtBusy === r.lvl}
                      title={al ? `Alerting when ${contract ? contract.sym : 'the board'} settles ≥ ${money(r.lvl)} — click to stop` : `Email me when ${contract ? contract.sym : 'the board'} settles at or above ${money(r.lvl)}`}
                      style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, padding: '2px 7px', borderRadius: 5, cursor: tgtBusy === r.lvl ? 'wait' : 'pointer',
                        border: `1px solid ${al ? '#c8a24a' : '#d8e2d8'}`, background: al ? '#fdf6e8' : '#fff', color: al ? '#7a4a00' : '#677167' }}>
                      {tgtBusy === r.lvl ? '…' : al ? '🔔 on' : '🔔'}
                    </button>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
          )}
          <div style={{ fontSize: 10, color: '#677167', marginTop: 8 }}>
            Analytical reference levels on {contract ? contract.sym : 'the board'}, from that contract's own technicals and how far this market has historically rallied. Not targets and not a recommendation to sell — a farmer-established target is recorded on the Targets page. {isHarvestMo ? 'Delivery timing does not drive pricing timing.' : 'Deferred month — carry is in the price.'}
          </div>

          {/* Everything currently WATCHED on this crop, across every board — not just
              the board on screen. A farm markets several contract months at once, so a
              list scoped to the current dropdown would hide half of them. */}
          {GRAIN_TARGET_ALERTS_ENABLED && tgtAlerts.length > 0 && (
            <div style={{ marginTop: 10, background: '#fdfaf2', border: '1px solid #ece0c4', borderRadius: 8, padding: '8px 11px' }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.07em', color: '#7a4a00', textTransform: 'uppercase', marginBottom: 6 }}>
                🔔 Watching {tgtAlerts.length} {crop.toLowerCase()} target{tgtAlerts.length > 1 ? 's' : ''}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {tgtAlerts.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, opacity: a.active ? 1 : 0.5 }}>
                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 800, color: '#1a2e1a', minWidth: 54 }}>{money(Number(a.threshold_value))}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", color: '#7a4a00', fontWeight: 700 }}>{a.contract_sym || 'any board'}</span>
                    {a.bushels ? <span style={{ color: '#677167' }}>{Math.round(a.bushels).toLocaleString()} bu</span> : null}
                    {a.last_fired_at ? <span style={{ color: '#1f7a3d', fontWeight: 700 }}>· hit {new Date(a.last_fired_at).toLocaleDateString()}</span> : null}
                    <span style={{ flex: 1 }} />
                    <button type="button" onClick={() => grainToggleTargetAlert(a).then(reloadTgtAlerts)} style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 4, border: '1px solid #d8e2d8', background: '#fff', color: a.active ? '#2d5a2d' : '#677167', cursor: 'pointer' }}>{a.active ? 'on' : 'off'}</button>
                    <button type="button" onClick={() => grainDeleteTargetAlert(a.id).then(reloadTgtAlerts)} style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 4, border: '1px solid #eccaca', background: '#fff', color: '#c0392b', cursor: 'pointer' }}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10.5, color: '#9a8a6a', marginTop: 6, lineHeight: 1.45 }}>
                Checked against the daily USDA settle — you and the agency both get the email, once per settle. It reports that your level printed; whether to price is your call.
              </div>
            </div>
          )}
          {/* State the horizon and the sample WITH the number. A bare "60%" invites reading it as
              a forecast for this market; it's a base rate off past days, and the two caveats that
              actually change how you'd use it are the window (a touch counts — which is exactly
              what a resting order needs) and the fact that it's history, not a view. */}
          {showOdds ? (
            <div style={{ fontSize: 10, color: '#677167', marginTop: 4, lineHeight: 1.5 }}>
              <b>Fills · ~{oddsMo} mo</b> — of the last 5 years of front-month days, the share whose best rally reached
              that level within the <b>~{oddsMo} mo this board still has to work</b>, normalized for how volatile this
              market is right now. It counts a touch <i>any time</i> in that window — which is what a resting order
              needs. A measured base rate, <b>not a forecast</b>: past days, not a view on this one.
            </div>
          ) : null}
        </div>
        )}

        {/* every board — the 3 target levels to rest on each contract month (Market Intelligence view) */}
        {generic && boardTargets.length ? (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              <div style={capLbl}>{slot ? `${crop} — 3 targets for ${slot.period} delivery` : `${crop} — 3 targets on every board`}</div>
              {/* These levels are DERIVED, never stored: each board's ladder is recomputed
                  from its own bars on every load, so a new settle moves the targets. The
                  stamp is the guard — if the USDA settle cron stalls, "3d ago" says so
                  instead of the levels quietly going stale. */}
              <FreshAsOf date={latestTs(boardTargets.map(b => ({ d: b.tc.asOf })), 'd')} label="settles" icon="📈" />
            </div>
            {/* ── the shareable strip: whose board, and whose costs ──
                A farmer asks "what should I be doing?" — he needs a CASH number at HIS elevator
                and a sense of whether it pays. Both are here, both are editable, neither moves a
                target. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', background: '#f6f9f5', border: '1px solid #e6efe6', borderRadius: 8, padding: '8px 11px', marginBottom: 10, fontSize: 11.5, color: '#5c6f5c' }}>
              {(() => {
                // House → term → point. Every cash figure in the grid below is priced off
                // THIS selection, so the control set has to speak the way the sale is
                // arranged: which house, delivered or picked up, which delivery point.
                //
                // ⚠ THE HOUSE SELECT RENDERS EVEN WITH NOTHING SELECTED (2026-08-28).
                // It used to be inside an `elevPick ? … :` gate, and elevPick was never
                // null because elevSrc defaulted to the rank-sorted first elevator — so
                // the page always arrived with a market already chosen for the farmer.
                // Now the default is EMPTY and the targets are futures-only until a human
                // picks. Silas does not choose the counterparty.
                const sel = { fontSize: 11.5, fontWeight: 700, padding: '3px 6px', borderRadius: 6, border: '1px solid #cfe0cf', background: '#fff', color: '#1a2e1a' };
                const houses = elevators.reduce((a, e) => (a.includes(e.house) ? a : a.concat(e.house)), []);
                if (!houses.length) return <span style={{ color: '#b8860b' }}>No posted local board for {crop.toLowerCase()} — futures targets only</span>;
                return (
                  // A span, not a label: four controls, none of which "Cash at" labels alone.
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700 }}>Cash at</span>
                    <select value={elevPick ? elevPick.cur.house : ''} onChange={e => (e.target.value ? pickHouse(e.target.value) : setElevator(null))} style={sel} title="Which house you're selling to — your choice, not a Silas recommendation">
                      <option value="">— select a market —</option>
                      {houses.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {!elevPick && (
                      <span style={{ color: '#b8860b' }}>
                        Futures targets only until you pick a market. <b>Cash targets use the selected market — not a recommendation of buyer.</b>
                      </span>
                    )}
                    {elevPick && elevPick.terms.length > 1 ? (
                      <select value={elevPick.cur.term} onChange={e => pickTerm(e.target.value)} style={sel} title="Delivered by you, or picked up at the farm — a different basis, and NOT comparable with the other structure">
                        {elevPick.terms.map(t => <option key={t} value={t}>{GRAIN_TERM_LBL[t] || t}</option>)}
                      </select>
                    ) : null}
                    {elevPick && elevPick.points.length > 1 ? (
                      <select value={elevPick.cur.src} onChange={e => setElevator(e.target.value)} style={sel} title="Delivery point">
                        {elevPick.points.map(p => <option key={p.src} value={p.src}>{p.place}</option>)}
                      </select>
                    ) : (elevPick && elevPick.cur.place) ? <span style={{ fontWeight: 700, color: '#1a2e1a' }}>{elevPick.cur.place}</span> : null}
                    {/* The delivery the farmer is actually asking about. "Every board" keeps
                        the survey view (each board priced off its own delivery month); pick a
                        slot and the whole card becomes a quote for that one delivery. */}
                    {elevPick && slotList.length > 1 ? (
                      <select value={shipSlot ?? ''} onChange={e => setShipSlot(e.target.value || null)} style={sel} title="Which delivery he's asking about — the basis is posted per slot, not per board">
                        <option value="">Every board</option>
                        {slotList.map(s => <option key={s.period} value={s.period}>ship {s.period}</option>)}
                      </select>
                    ) : null}
                  </span>
                );
              })()}
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontWeight: 700 }}>Cost</span>
                <input type="number" value={copOv[crop]?.cost ?? ''} placeholder={String(copDefault.cost ?? '')}
                  onChange={e => setCopOv(p => ({ ...p, [crop]: { ...p[crop], cost: e.target.value } }))}
                  style={{ width: 58, fontSize: 11.5, padding: '3px 5px', borderRadius: 6, border: '1px solid #cfe0cf' }} /> $/ac ÷
                <input type="number" value={copOv[crop]?.yield ?? ''} placeholder={String(copDefault.yield ?? '')}
                  onChange={e => setCopOv(p => ({ ...p, [crop]: { ...p[crop], yield: e.target.value } }))}
                  style={{ width: 48, fontSize: 11.5, padding: '3px 5px', borderRadius: 6, border: '1px solid #cfe0cf' }} /> bu/ac
                {copDefault.byproduct ? <span style={{ fontSize: 10, color: '#677167' }}>less ${copDefault.byproduct}/ac straw</span> : null}
              </span>
              {breakevenBu ? (
                <span style={{ fontWeight: 800, color: '#1a2e1a' }}>Breakeven {money(breakevenBu)}/bu</span>
              ) : null}
              {/* the posted board's OWN date — futures and basis go stale independently, and a
                  yesterday's-basis cash target quietly misquotes the farmer */}
              {cashBids.length ? <FreshAsOf date={latestTs(cashBids.filter(r => r.source === elevSrc), 'bid_date')} label="basis" icon="🏷" /> : null}
            </div>
            {/* Rough $/bu cost guide from the WNY Model Farm budget — so a farmer who doesn't
                know their own cost still gets a ballpark to weigh a target against. Always shown
                (survives edits to the strip above), all three crops, labeled as an estimate. */}
            <div style={{ fontSize: 10.5, color: '#60735f', marginTop: -4, marginBottom: 10, lineHeight: 1.5 }}>
              💡 No cost figure handy? Rough WNY guide <span style={{ color: '#b0bcb0' }}>(Model Farm budget)</span>:{' '}
              {['Corn', 'Wheat', 'Soybeans'].map((cr, i) => {
                const d = GRAIN_COP_DEFAULTS[cr]; const be = grainBreakeven(d.cost, d.yield, d.byproduct || 0);
                return <span key={cr}><b style={{ color: cr === crop ? '#2d6a2d' : '#60735f' }}>{cr.toLowerCase()} ≈ {money(be)}</b>{i < 2 ? ' · ' : ''}</span>;
              })}
              <span> /bu</span> — swap in the farmer's real numbers above when you have them.
            </div>
            {/* Say the quote out loud before the numbers: which delivery, to where, off which
                board, at what basis. Two slots off one board can carry different basis, so
                naming the slot is what makes the cash column checkable against the sheet. */}
            {slot ? (
              <div style={{ fontSize: 11.5, color: '#2d4a2d', background: '#eef6ea', border: '1px solid #cfe4c4', borderRadius: 8, padding: '8px 11px', marginBottom: 10, lineHeight: 1.5 }}>
                <b>{slot.period}</b> {elevPick && elevPick.cur.term === 'pickup' ? 'picked up' : 'delivered'} to <b>{elevPick ? elevPick.cur.place : ''}</b> ships off <b>{boardKey(slot.cme)}</b> · basis <b>{slot.basis >= 0 ? '+' : '−'}{Math.abs(Math.round(slot.basis * 100))}¢</b>.
                {gridBoards.length
                  ? <> The three targets below are <b>cash for that delivery</b> — {boardKey(slot.cme)} futures {slot.basis >= 0 ? '+' : '−'} {Math.abs(Math.round(slot.basis * 100))}¢.</>
                  : <> <b style={{ color: '#a5461b' }}>{boardKey(slot.cme)} isn't a tradeable board right now</b> — the elevator still posts the slot, but there's no futures month left to rest an order on. Price it at market or pick a further slot.</>}
              </div>
            ) : null}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>
                  {['Board', 'Last', 'Target 1 · attainable', 'Target 2 · strong', 'Target 3 · flyer'].map((h, i) => (
                    <th key={h} style={{ textAlign: i < 2 ? 'left' : 'right', padding: '0 8px 5px', fontSize: 10, fontWeight: 800, color: '#677167', textTransform: 'uppercase', letterSpacing: '.05em' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {gridBoards.map(b => (
                    <tr key={b.sym} style={{ borderBottom: '1px solid #eef3ee', background: contract && b.sym === contract.sym ? '#f6f9f5' : 'transparent' }}>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 800, color: '#1a2e1a' }}>{MON[b.mo]} '{String(b.yr).slice(2)}</div>
                        <div style={{ fontSize: 10, color: '#677167' }}>{b.sym}{b.mo === GRAIN_HARVEST_MO[crop] ? ' · harvest' : ''}</div>
                        {/* the window the fill-% below is measured over — a near board's order
                            has far less time to work, so its rungs are (correctly) tighter */}
                        <div style={{ fontSize: 10.5, color: '#b0bcb0' }}>{b.horizonLbl}</div>
                      </td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 700, color }}>{money(b.tc.last)}</div>
                        <div style={{ fontSize: 10, color: b.regime === 'downtrend' ? '#b8860b' : '#1f7a3d' }}>
                          {b.regime}{b.rsi != null ? ` · RSI ${Math.round(b.rsi)}${b.rsi >= 70 ? ' ⚠' : ''}` : ''}
                        </div>
                        <div style={{ fontSize: 10.5, color: '#677167' }}>
                          {b.mktPct == null ? '' : `${grainOrd(Math.round(b.mktPct * 100))} pct of 2-yr`}
                        </div>
                      </td>
                      {[0, 1, 2].map(i => {
                        const t = b.levels[i];
                        if (!t) return <td key={i} style={{ padding: '8px', textAlign: 'right', color: '#c3cfc3' }}>—</td>;
                        // A farmer rests a CASH order at his elevator, not a futures order. Futures
                        // + that board's posted basis = the number he'd actually give the merchandiser.
                        const bs = pickBasis(b.sym, b.mo, b.yr);
                        const cash = bs ? t.lvl + Number(bs.delivery_basis) : null;
                        const clears = (cash != null && breakevenBu) ? cash - breakevenBu : null;
                        return (
                          <td key={i} style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {cash != null ? (
                              <>
                                <div style={{ fontWeight: 800, color: '#1a2e1a', fontSize: 15 }}>{money(cash)}<span style={{ fontSize: 10, fontWeight: 600, color: '#677167' }}> cash</span></div>
                                {/* name the exact posted bid this came from — several delivery periods
                                    price off one contract at different basis, so "which one" matters */}
                                <div style={{ fontSize: 10, color: '#60735f' }} title={`${bs.delivery_period} basis, posted ${bs.bid_date}`}>
                                  {money(t.lvl)} {Number(bs.delivery_basis) >= 0 ? '+' : '−'} {Math.abs(Math.round(Number(bs.delivery_basis) * 100))}¢ <span style={{ color: '#677167' }}>{bs.delivery_period}</span>
                                </div>
                              </>
                            ) : (
                              <div style={{ fontWeight: 800, color: '#1a2e1a', fontSize: 15 }}>{money(t.lvl)}<span style={{ fontSize: 10, fontWeight: 600, color: '#677167' }}> futures</span></div>
                            )}
                            {/* what the level IS — and when a rung is only a distance, say so
                                rather than dressing it up as a level that isn't there */}
                            <div style={{ fontSize: 10, color: t.measured ? '#b0bcb0' : '#5c6f5c', fontStyle: t.measured ? 'italic' : 'normal' }}>
                              {t.lbl}{t.also && t.also.length ? ` + ${t.also[0]}` : ''}
                            </div>
                            {/* THE number that makes "too aggressive" arguable instead of a vibe:
                                how often this board historically got this high in the time it has. */}
                            <div style={{ fontSize: 10.5, color: '#677167' }}>
                              +{Math.round((t.lvl - b.tc.last) * 100)}¢
                              {t.fill != null ? <> · <b style={{ color: t.fill >= 0.5 ? '#1f7a3d' : t.fill >= 0.3 ? '#5c6f5c' : '#b8860b' }}>fills {Math.round(t.fill * 100)}%</b></> : null}
                            </div>
                            {/* Breakeven COLORS the rung. It never moved it. */}
                            {clears != null ? (
                              <div style={{ fontSize: 10.5, fontWeight: 700, color: clears >= 0 ? '#1f7a3d' : '#b8860b' }}>
                                {clears >= 0 ? `+${Math.round(clears * 100)}¢ over cost` : `${Math.round(clears * 100)}¢ under cost`}
                              </div>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* The one thing that must not be misread when this gets shared with a farmer. */}
            {breakevenBu ? (
              <div style={{ fontSize: 11, color: '#7a4a00', background: '#fbf1e3', borderRadius: 8, padding: '8px 11px', marginTop: 10, lineHeight: 1.5 }}>
                <b>Below cost is not a reason to refuse a sale.</b> The market does not price your cost of production — it will not pay you more because the crop was expensive to grow. Breakeven tells you whether a price pays the bills; it does not tell you whether a better one is coming. A target set above a level the market can't reach isn't protection, it's a wish, and the crop still ends up sold — just later, and usually lower.
              </div>
            ) : null}
            <div style={{ fontSize: 10, color: '#677167', marginTop: 8, lineHeight: 1.5 }}>
              Three analytical reference levels per board, placed by <b>how often this market actually gets there</b>. Five years of front-month history (roll-adjusted) was asked the same question on every past day: how far did it rally above that price, at its best, within the time <b>this board still has to work</b>? That measured distribution places the levels — <b>attainable</b> (~75% of the time), <b>strong</b> (~40%), and a <b>flyer</b> (~18%) for the weather market. Each is then <b>snapped to the nearest real level</b> (moving average, prior high, Fibonacci retracement or extension, round number), and the <i>fill %</i> shown is recomputed for the price it actually landed on. Rungs widen in a volatile market, tighten in a quiet one, and a near board gets a tighter ladder than a deferred one because its order has less time to work. Nothing here is a target or a recommendation to sell: these are historical price context, and a target exists only when a farmer establishes one. <b>Not forecasts</b> — a reach rate is what the past did, not a promise. Educational — not a solicitation to trade.
            </div>
          </div>
        ) : null}

        {/* The "% sold by date" bar graphic was removed 2026-07-14 (Kevin: "they are confusing").
            Its job — a dated pricing pace — is now the tranche schedule on the Marketing Plan
            tab, which shows the SAME discipline as concrete orders (target OR decision date). */}

        {/* carry — store vs sell, net of storage + interest (cost of carry) */}
        {carryAnalysis && carryAnalysis.best && (() => {
          const b = carryAnalysis.best;
          const bd = new Date(b.month + 'T00:00:00');
          const bmLbl = `${MON[bd.getUTCMonth() + 1]} '${String(bd.getUTCFullYear()).slice(2)}`;
          const pays = b.net > 0.005;
          const signC = v => (v >= 0 ? '+' : '−') + Math.abs(Math.round(v * 100)) + '¢';
          const tdL = { padding: '5px 6px', color: '#5c6f5c' };
          const tdR = (c) => ({ padding: '5px 6px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: "tabular-nums", color: c });
          const inp = { width: 46, fontSize: 11, padding: '3px 5px', borderRadius: 5, border: '1px solid #cfe0cf' };
          const nd = new Date(carryAnalysis.near + 'T00:00:00');
          const nearLbl = `${MON[nd.getUTCMonth() + 1]} '${String(nd.getUTCFullYear()).slice(2)}`;
          // Deliver-at-harvest / open-storage bushels can ONLY be priced off the front
          // month — the elevator holds the grain and keeps the carry via storage charges.
          // Capturing carry needs on-farm bins (sell a deferred contract, deliver later).
          if (storageMode === 'deliver') {
            return (
              <div style={card}>
                <div style={capLbl}>Carry — not capturable once delivered</div>
                <div style={{ fontSize: 11.5, color: '#7a4a00', marginTop: 6, lineHeight: 1.5 }}>
                  Delivered / open-storage bushels price off the <b>nearby ({nearLbl})</b> only. Once grain is in the elevator's open storage the <b>carry is the elevator's</b> (kept via storage charges) — not yours to capture. Capturing carry needs <b>on-farm bins</b>: sell a deferred contract and deliver later.
                </div>
                <div style={{ fontSize: 10, color: '#677167', marginTop: 6 }}>The market pays {signC(b.gross)} to carry to {bmLbl}, but that isn't capturable on open-storage bushels.</div>
              </div>
            );
          }
          return (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                <div style={capLbl}>Carry — deferred vs. nearby</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: pays ? '#1f7a3d' : '#b8860b' }}>{pays ? 'carry beats holding cost' : 'carry under holding cost'}</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <tbody>
                  <tr><td style={tdL}>Market carry to {bmLbl} ({b.m} mo)</td><td style={tdR(b.gross >= 0 ? '#1a2e1a' : '#b8860b')}>{signC(b.gross)}</td></tr>
                  <tr><td style={tdL}>− Storage ({b.m} mo × {(storagePerMo * 100).toFixed(1)}¢{storageMode === 'deliver' ? ' · elevator' : storageMode === 'bins' ? ' · on-farm' : ''})</td><td style={tdR('#a5461b')}>−{Math.round(b.storage * 100)}¢</td></tr>
                  <tr><td style={tdL}>− Interest ({money(carryAnalysis.nearP)} × {(interestRate * 100).toFixed(0)}% × {b.m}/12)</td><td style={tdR('#a5461b')}>−{Math.round(b.interest * 100)}¢</td></tr>
                  <tr style={{ borderTop: '2px solid #e6efe6' }}><td style={{ ...tdL, fontWeight: 800, color: '#1a2e1a' }}>= Net carry</td><td style={{ ...tdR(pays ? '#1f7a3d' : '#b8860b'), fontWeight: 800, fontSize: 14 }}>{signC(b.net)}</td></tr>
                </tbody>
              </table>
              <div style={{ fontSize: 10.5, color: pays ? '#1f7a3d' : '#5c6f5c', marginTop: 8, fontWeight: pays ? 700 : 400 }}>
                {pays
                  ? `The market pays ${signC(b.gross)} to carry to ${bmLbl} — above your ${Math.round(b.cost * 100)}¢ storage + interest. If you're pricing for that window, the ${bmLbl} board locks ${signC(b.net)}/bu more than the nearby.`
                  : `Carry to ${bmLbl} (${signC(b.gross)}) doesn't cover your ${Math.round(b.cost * 100)}¢ storage + interest, so a deferred contract won't out-price the nearby. Holding bushels UNPRICED to catch a rally is a separate market call — not a carry decision.`}
              </div>
              {storageMode === 'split' ? <div style={{ fontSize: 10, color: '#7a4a00', marginTop: 6 }}>Split delivery: carry applies to the <b>stored</b> bushels; the harvest-delivered bushels are in open storage and price off the nearby.</div> : null}
              <div style={{ fontSize: 10.5, color: '#677167', marginTop: 6 }}>Carry compares pricing a deferred contract vs. the nearby (a spread) — it is not a forecast of where flat price goes.</div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, fontSize: 11, color: '#60735f' }}>
                <label>storage <input type="number" value={storageCentsMo} onChange={e => setStorageCentsMo(e.target.value)} placeholder={String(storageDefault)} style={inp} /> ¢/bu·mo</label>
                <label>interest <input type="number" value={interestPct} onChange={e => setInterestPct(e.target.value)} placeholder="8" style={inp} /> %/yr</label>
              </div>
            </div>
          );
        })()}

        {/* seasonal milestone % schedule removed per request — sizing/pacing is the agent's call */}
      </>)}
    </div>
  );
}

// Self-contained markets panel — futures curve, corn/soy ratio, fund positioning
// (COT), and local basis. Reused on the Grain Marketing "Markets" tab AND the
// Market Intelligence page. All data from free feeds (USDA futures + CFTC COT +
// our basis).
function GrainMarketsPanel({ isMobile = false }) {
  const [board, setBoard] = useState(null); // { commodities, grain_as_of, ratio }
  const [cot, setCot] = useState(null);     // { corn:{...}, soybeans, wheat, ... }
  const [basis, setBasis] = useState(null); // basis_calculated_daily series
  const [crop, setCrop] = useState('Corn');

  useEffect(() => {
    fetch('/api/market?fn=futures').then(r => (r.ok ? r.json() : null)).then(d => { if (d && d.commodities) setBoard(d); }).catch(() => {});
    fetch('/api/market?fn=cot-ag').then(r => (r.ok ? r.json() : null)).then(d => { if (d) setCot(d); }).catch(() => {});
  }, []);

  const commodity = GRAIN_CROP_TO_COMMODITY[crop];
  useEffect(() => {
    let cancelled = false;
    setBasis(null);
    // authedFetch, not fetch: the endpoint withholds Howlett's series from a
    // non-admin while the Cash Board switch is off (api/_howlett-gate.js), and
    // an anonymous request would read as non-admin for Kevin and Mike too.
    authedFetch(`/api/market?fn=basis-history&commodity=${commodity}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled) setBasis(d && Array.isArray(d.series) ? d.series : []); })
      .catch(() => { if (!cancelled) setBasis([]); });
    return () => { cancelled = true; };
  }, [commodity]);

  const money = (v, d = 2) => (v == null || isNaN(v) ? '—' : '$' + Number(v).toFixed(d));
  const intFmt = (v) => (v == null || isNaN(v) ? '—' : Number(v).toLocaleString());
  const color = GRAIN_COLORS[crop];
  const curve = board?.commodities?.[commodity] || [];
  const ratio = board?.ratio;
  const cotC = cot?.[commodity];
  // Local cash-basis board. The basis-history feed carries MANY elevators (WNY
  // Energy + several Deerfield/OH yards) across every delivery period, so we pin
  // each crop to its intended LOCAL posting elevator — otherwise the "latest row"
  // is whichever source happened to sort last (was showing an OH Deerfield bid
  // under a hardcoded "WNY Energy" label). We then list that elevator's current
  // posted board (one row per delivery period) rather than a mixed-source chart.
  const LOCAL_BASIS_SRC = { Corn: 'wny_energy', Wheat: 'star_of_west' };
  const BASIS_SRC_LABEL = { wny_energy: 'WNY Energy (Medina)', star_of_west: 'Star of the West (Churchville)', adm_buffalo: 'ADM Buffalo' };
  let localBasisSrc = LOCAL_BASIS_SRC[crop] || null;
  let srcRows = localBasisSrc ? (basis || []).filter(b => b.source === localBasisSrc) : [];
  // Resilience: if the pinned local elevator has no bids right now but we DO have
  // basis rows (e.g. a scraper gap on WNY Energy), fall back to the freshest posted
  // board from another WNY-local elevator (Howlett) so the card never goes blank.
  // The header label reflects whichever elevator is actually shown.
  if (!srcRows.length && (basis || []).length) {
    const latest = basis.reduce((mx, b) => (b.bid_date > mx ? b.bid_date : mx), '');
    const onLatest = basis.filter(b => b.bid_date === latest);
    const pick = onLatest.find(b => /howlett|wny/i.test(b.source)) || onLatest[0];
    if (pick) { localBasisSrc = pick.source; srcRows = basis.filter(b => b.source === pick.source); }
  }
  const postedDate = srcRows.length ? srcRows[srcRows.length - 1].bid_date : null;   // rows come bid_date-ascending
  // Sort posted bids nearest → deferred by the futures contract they're quoted against (e.g. CN26).
  const contractOrd = (c) => { const m = String(c || '').match(/([FGHJKMNQUVXZ])(\d{2})$/); return m ? parseInt(m[2], 10) * 12 + 'FGHJKMNQUVXZ'.indexOf(m[1]) : 9999; };
  const fmtPeriod = (dp) => {
    const MON = { JAN: 'Jan', FEB: 'Feb', MAR: 'Mar', APR: 'Apr', MAY: 'May', JUN: 'Jun', JUL: 'Jul', AUG: 'Aug', SEP: 'Sep', OCT: 'Oct', NOV: 'Nov', DEC: 'Dec' };
    const m = String(dp || '').toUpperCase().match(/^([A-Z]{3})([A-Z]{3})?(\d{2})$/);
    if (m && MON[m[1]]) return m[2] && MON[m[2]] ? `${MON[m[1]]}–${MON[m[2]]} ${m[3]}` : `${MON[m[1]]} ${m[3]}`;
    return dp ? dp.charAt(0) + dp.slice(1).toLowerCase() : '';
  };
  const postedBasis = srcRows.filter(b => b.bid_date === postedDate)
    .sort((a, b) => contractOrd(a.delivery_contract) - contractOrd(b.delivery_contract));

  const card = { background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, padding: '14px 16px' };
  const capLbl = { fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#4a6e4a', textTransform: 'uppercase' };

  return (
    <div>
      {/* crop selector + freshness */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['Corn', 'Soybeans', 'Wheat'].map(c => (
            <button key={c} onClick={() => setCrop(c)} style={{
              padding: '7px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              fontWeight: crop === c ? 800 : 600, border: '1px solid',
              background: crop === c ? GRAIN_COLORS[c] : '#fff', color: crop === c ? '#fff' : '#4a6e4a',
              borderColor: crop === c ? GRAIN_COLORS[c] : '#c8d8c0',
            }}>{GRAIN_TICKER[c]} · {c}</button>
          ))}
        </div>
        {board?.grain_as_of && (
          <div style={{ fontSize: 10, color: '#2d6a1a', fontWeight: 700 }}>● Live · USDA settled {board.grain_as_of}</div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
        {/* A — Futures curve */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <span style={capLbl}>{crop} Futures Curve</span>
            <span style={{ fontSize: 11, color: '#60735f' }}>{curve.length ? `${money(curve[0].settle_price)} ${curve[0].contract_month}` : ''}</span>
          </div>
          <GrainTinyChart points={curve.map(r => ({ y: r.settle_price }))} color={color} height={100} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 8 }}>
            <tbody>
              {curve.map((r, i) => (
                <tr key={r.contract_month} style={{ borderTop: i ? '1px solid #f0f4ee' : 'none' }}>
                  <td style={{ padding: '3px 4px', color: '#60735f' }}>{r.contract_month}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: '#1a2e1a' }}>{money(r.settle_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* B — Corn/Soy ratio */}
        <div style={card}>
          <div style={capLbl}>Corn / Soybean Ratio</div>
          {ratio ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 40, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: ratio.value >= 2.5 ? '#3a6e32' : '#c8860a' }}>{Number(ratio.value).toFixed(2)}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: ratio.value >= 2.5 ? '#3a6e32' : '#c8860a' }}>{ratio.signal}</span>
              </div>
              {/* pivot gauge 2.0 .. 3.0 */}
              {(() => {
                const lo = 2.0, hi = 3.0, clamp = Math.max(lo, Math.min(hi, ratio.value));
                const pct = ((clamp - lo) / (hi - lo)) * 100, pivot = ((2.5 - lo) / (hi - lo)) * 100;
                return (
                  <div style={{ position: 'relative', height: 10, background: 'linear-gradient(90deg,#f2e6cf,#e6efe0)', borderRadius: 5, margin: '14px 0 4px' }}>
                    <div style={{ position: 'absolute', left: `${pivot}%`, top: -3, bottom: -3, width: 2, background: '#60735f' }} />
                    <div style={{ position: 'absolute', left: `calc(${pct}% - 6px)`, top: -3, width: 12, height: 12, borderRadius: 6, background: ratio.value >= 2.5 ? '#3a6e32' : '#c8860a', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  </div>
                );
              })()}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#677167' }}><span>2.0 · corn</span><span>2.5 pivot</span><span>3.0 · beans</span></div>
              <div style={{ fontSize: 11, color: '#60735f', marginTop: 10, lineHeight: 1.5 }}>
                {ratio.soy_contract} soybeans <strong>{money(ratio.soy)}</strong> ÷ {ratio.corn_contract} corn <strong>{money(ratio.corn)}</strong>. Above ~2.5 the market pays more to grow soybeans → signals more bean acres; below ~2.4 favors corn.
              </div>
            </div>
          ) : <div style={{ color: '#aaa', fontSize: 12, marginTop: 10 }}>Ratio unavailable.</div>}
        </div>

        {/* C — Fund positioning (COT) */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <span style={capLbl}>{crop} Fund Positioning</span>
            {cotC && <span style={{ fontSize: 10, color: '#677167' }}>CFTC {cotC.report_date}</span>}
          </div>
          {cotC ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: cotC.mm_net >= 0 ? '#3a6e32' : '#c0392b' }}>{cotC.mm_net >= 0 ? '+' : ''}{intFmt(cotC.mm_net)}</span>
                <span style={{ fontSize: 12, color: '#60735f' }}>managed-money net {cotC.mm_net >= 0 ? 'long' : 'short'}</span>
              </div>
              <div style={{ fontSize: 10, color: '#677167', marginTop: 2 }}>{intFmt(cotC.mm_long)} long / {intFmt(cotC.mm_short)} short</div>
              {/* 52-wk range gauge */}
              {cotC.wk52_pct != null && (
                <>
                  <div style={{ position: 'relative', height: 10, background: '#eef2ec', borderRadius: 5, margin: '12px 0 4px' }}>
                    <div style={{ position: 'absolute', left: `calc(${(cotC.wk52_pct * 100).toFixed(0)}% - 6px)`, top: -3, width: 12, height: 12, borderRadius: 6, background: cotC.wk52_pct > 0.8 ? '#c0392b' : cotC.wk52_pct < 0.2 ? '#3a6e32' : '#60735f', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#677167' }}><span>52wk low {intFmt(cotC.wk52_min)}</span><span>{Math.round(cotC.wk52_pct * 100)}%ile</span><span>high {intFmt(cotC.wk52_max)}</span></div>
                </>
              )}
              <GrainTinyChart points={(cotC.history || []).map(h => ({ y: h.net }))} color="#4a6e4a" height={70} zeroLine fill={false} />
              <div style={{ fontSize: 11, color: '#60735f', marginTop: 6, lineHeight: 1.5 }}>
                {cotC.wk52_pct > 0.8 ? 'Funds crowded long near a 52-week high — a contrarian caution; crowded longs can unwind fast.'
                  : cotC.wk52_pct < 0.2 ? 'Funds heavily short near a 52-week low — room to cover if fundamentals turn.'
                  : 'Sentiment indicator, not a timing tool — cross-reference with fundamentals & basis.'}
              </div>
            </div>
          ) : <div style={{ color: '#aaa', fontSize: 12, marginTop: 10 }}>Positioning unavailable.</div>}
        </div>

        {/* D — Local basis: current posted board (one row per delivery period) */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <span style={capLbl}>{crop} Local Basis</span>
            {postedDate && <span style={{ fontSize: 10, color: '#677167' }}>{postedDate}</span>}
          </div>
          {basis == null ? <div style={{ color: '#aaa', fontSize: 12, marginTop: 10 }}>Loading…</div>
            : postedBasis.length ? (
              <div>
                <div style={{ fontSize: 10, color: '#677167', marginBottom: 8 }}>{BASIS_SRC_LABEL[localBasisSrc] || (localBasisSrc || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} · posted cash bids ($/bu)</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>
                    {['Delivery', 'Cash', 'Fut', 'Basis'].map((h, i) => (
                      <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '3px 4px', fontSize: 10, fontWeight: 800, color: '#60735f', textTransform: 'uppercase', borderBottom: '1px solid #dde8d8' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {postedBasis.map((b, i) => {
                      const bas = Number(b.delivery_basis);
                      return (
                        <tr key={`${b.delivery_period}-${i}`} style={{ borderBottom: '1px solid #f0f4ee' }}>
                          <td style={{ padding: '5px 4px', color: '#4a6e4a', fontWeight: i === 0 ? 700 : 400 }}>{fmtPeriod(b.delivery_period)}{i === 0 && <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#1a4a7a', letterSpacing: '0.04em' }}>NEARBY</span>}</td>
                          <td style={{ padding: '5px 4px', textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{money(b.cash_price)}</td>
                          <td style={{ padding: '5px 4px', textAlign: 'right', fontVariantNumeric: "tabular-nums", color: '#677167', fontSize: 11 }}>{b.delivery_contract}</td>
                          <td style={{ padding: '5px 4px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 800, color: isNaN(bas) ? '#677167' : bas >= 0 ? '#1a6a3a' : '#c0392b' }}>{isNaN(bas) ? '—' : `${bas >= 0 ? '+' : ''}${bas.toFixed(2)}`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: '#8a9a88', fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
                No local cash-basis feed for {crop.toLowerCase()} yet. Corn (WNY Energy, Medina) and wheat (Star of the West, Churchville) are tracked; soybean basis can be added when a posting elevator feed is wired.
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

// Forward-curve chart with a theoretical full-carry overlay. The gap between the
// two lines is how much of full carry the market is (or isn't) paying — the
// single most useful merchandiser visual.
function ElevatorCurveChart({ curve, storage, interest, color, height = 230 }) {
  if (!curve || curve.length < 2) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 12 }}>Curve unavailable</div>;
  const W = 580, H = height, padL = 46, padR = 14, padT = 16, padB = 34;
  const stor = Number(storage), rate = Number(interest) / 100;
  const s0 = curve[0].settle_price;
  const mFromFront = i => {
    const da = new Date(curve[0].contract_date + 'T00:00:00Z'), db = new Date(curve[i].contract_date + 'T00:00:00Z');
    return (db.getUTCFullYear() - da.getUTCFullYear()) * 12 + (db.getUTCMonth() - da.getUTCMonth());
  };
  const fc = curve.map((c, i) => s0 + (stor * mFromFront(i)) / 100 + s0 * rate * (mFromFront(i) / 12));
  const actual = curve.map(c => c.settle_price);
  const all = [...actual, ...fc];
  let min = Math.min(...all), max = Math.max(...all);
  const padY = (max - min) * 0.1 || 0.1; min -= padY; max += padY;
  const n = curve.length;
  const xAt = i => padL + (n <= 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (n - 1));
  const yAt = v => H - padB - ((v - min) / (max - min)) * (H - padT - padB);
  const actLine = curve.map((c, i) => `${xAt(i).toFixed(1)},${yAt(actual[i]).toFixed(1)}`).join(' ');
  const fcLine = curve.map((c, i) => `${xAt(i).toFixed(1)},${yAt(fc[i]).toFixed(1)}`).join(' ');
  const ticks = Array.from({ length: 5 }, (_, k) => min + (max - min) * k / 4);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {ticks.map((tv, k) => (
        <g key={k}>
          <line x1={padL} y1={yAt(tv)} x2={W - padR} y2={yAt(tv)} stroke="#eef2ec" strokeWidth="1" />
          <text x={padL - 6} y={yAt(tv) + 3} textAnchor="end" fontSize="9" fill="#677167" fontFamily="monospace">${tv.toFixed(2)}</text>
        </g>
      ))}
      <polyline points={fcLine} fill="none" stroke="#677167" strokeWidth="1.5" strokeDasharray="5 4" />
      <polygon points={`${xAt(0)},${H - padB} ${actLine} ${xAt(n - 1)},${H - padB}`} fill={color} opacity="0.07" />
      <polyline points={actLine} fill="none" stroke={color} strokeWidth="2.5" />
      {curve.map((c, i) => (
        <g key={i}>
          <circle cx={xAt(i)} cy={yAt(actual[i])} r="3.5" fill={color} />
          <text x={xAt(i)} y={yAt(actual[i]) - 8} textAnchor="middle" fontSize="9" fontWeight="700" fill="#1a2e1a" fontFamily="monospace">{actual[i].toFixed(2)}</text>
          <text x={xAt(i)} y={H - padB + 14} textAnchor="middle" fontSize="9" fill="#60735f" fontFamily="monospace">{c.contract_month}</text>
        </g>
      ))}
      <g>
        <line x1={padL + 4} y1={padT} x2={padL + 22} y2={padT} stroke={color} strokeWidth="2.5" />
        <text x={padL + 27} y={padT + 3} fontSize="9" fill="#4a6e4a">settle</text>
        <line x1={padL + 74} y1={padT} x2={padL + 92} y2={padT} stroke="#677167" strokeWidth="1.5" strokeDasharray="5 4" />
        <text x={padL + 97} y={padT + 3} fontSize="9" fill="#4a6e4a">full carry (theoretical)</text>
      </g>
    </svg>
  );
}

// Elevator / merchandiser view — DATA ONLY (no recommendations). Calendar-spread
// carry (matrix + full-carry chart), consecutive spreads, cross-market spreads
// (incl. wheat class spreads), fund positioning w/ weekly flow, and local basis.
function ElevatorPanel({ isMobile = false }) {
  const [board, setBoard] = useState(null);
  const [cot, setCot] = useState(null);
  const [cornBoard, setCornBoard] = useState(null);   // WNY Energy full latest board
  const [wheatBoard, setWheatBoard] = useState(null); // Star of the West full latest board
  const [crop, setCrop] = useState('Corn');
  const [storage, setStorage] = useState(4);   // ¢/bu/month commercial storage
  const [interest, setInterest] = useState(8); // % annual money cost
  const [matrixMode, setMatrixMode] = useState('pct'); // 'pct' | 'cents'
  const [wheatLeg, setWheatLeg] = useState('');   // Chicago wheat contract for the wheat−corn spread
  const [cornLeg, setCornLeg] = useState('');     // corn contract for the wheat−corn spread

  useEffect(() => {
    fetch('/api/market?fn=futures').then(r => (r.ok ? r.json() : null)).then(d => { if (d && d.commodities) setBoard(d); }).catch(() => {});
    fetch('/api/market?fn=cot-ag').then(r => (r.ok ? r.json() : null)).then(d => { if (d) setCot(d); }).catch(() => {});
  }, []);

  const commodity = GRAIN_CROP_TO_COMMODITY[crop];
  useEffect(() => {
    // Full latest-date posted board (all delivery periods) per elevator.
    const latestBoard = (bids, src) => {
      const rs = (bids || []).filter(r => r.source === src);
      if (!rs.length) return [];
      const latest = rs.reduce((m, r) => (r.bid_date > m ? r.bid_date : m), '');
      return rs.filter(r => r.bid_date === latest)
        .sort((a, b) => String(a.delivery_month_code || '').localeCompare(String(b.delivery_month_code || '')));
    };
    fetch('/api/market?fn=cash-bids&commodity=corn&days=10').then(r => (r.ok ? r.json() : null)).then(d => setCornBoard(latestBoard(d?.bids, 'wny_energy'))).catch(() => {});
    fetch('/api/market?fn=cash-bids&commodity=wheat&days=10').then(r => (r.ok ? r.json() : null)).then(d => setWheatBoard(latestBoard(d?.bids, 'star_of_west'))).catch(() => {});
  }, []);

  const curve = board?.commodities?.[commodity] || [];
  const ratio = board?.ratio;
  const cotC = cot?.[commodity];
  const color = GRAIN_COLORS[crop];
  const rate = Number(interest) / 100, stor = Number(storage);

  const money = (v, d = 2) => (v == null || isNaN(v) ? '—' : '$' + Number(v).toFixed(d));
  const c1 = (v) => (v == null || isNaN(v) ? '—' : (v >= 0 ? '+' : '') + v.toFixed(1) + '¢');
  const monthsBetween = (a, b) => {
    const da = new Date(a + 'T00:00:00Z'), db = new Date(b + 'T00:00:00Z');
    return (db.getUTCFullYear() - da.getUTCFullYear()) * 12 + (db.getUTCMonth() - da.getUTCMonth());
  };
  const carryCell = (i, j) => {
    if (!curve[i] || !curve[j]) return null;
    const m = monthsBetween(curve[i].contract_date, curve[j].contract_date);
    if (m <= 0) return null;
    const spread = (curve[j].settle_price - curve[i].settle_price) * 100; // ¢/bu
    const full = stor * m + curve[i].settle_price * rate * (m / 12) * 100; // ¢/bu
    const pct = full > 0 ? (spread / full) * 100 : null;
    return { m, spread, full, pct, perMo: spread / m };
  };
  const carryColor = (pct) => {
    if (pct == null) return '#f6f6f4';
    if (pct < 0) return `rgba(192,57,43,${Math.min(0.6, 0.22 + Math.abs(pct) / 100 * 0.4).toFixed(3)})`;
    if (pct >= 100) return `rgba(45,106,26,${Math.min(0.6, 0.35 + (pct - 100) / 100 * 0.3).toFixed(3)})`; // green = nets positive
    if (pct >= 67) return 'rgba(122,138,42,0.32)';   // olive — partial carry (store-for-basis zone)
    if (pct >= 50) return 'rgba(160,170,90,0.22)';
    return `rgba(200,134,10,${Math.min(0.42, 0.12 + pct / 100 * 0.3).toFixed(3)})`;
  };

  // ── derived metrics (data only) ──
  const consec = curve.slice(0, -1).map((_, i) => ({ from: curve[i].contract_month, to: curve[i + 1].contract_month, ...carryCell(i, i + 1) }));
  const frontToBack = curve.length >= 2 ? carryCell(0, curve.length - 1) : null;
  const spreadsArr = consec.map(s => s.spread);
  const curveState = spreadsArr.length === 0 ? '—'
    : (spreadsArr.some(s => s < 0) && spreadsArr.some(s => s > 0)) ? 'mixed'
    : spreadsArr.every(s => s <= 0) ? 'inverse' : 'carry';
  const NEWCROP_MONTH = { corn: 12, soybeans: 11, wheat: 7 };
  const ncIdx = curve.findIndex(c => Number(String(c.contract_date).slice(5, 7)) === NEWCROP_MONTH[commodity]);
  const oldNew = ncIdx > 0 ? { from: curve[ncIdx - 1].contract_month, to: curve[ncIdx].contract_month, ...carryCell(ncIdx - 1, ncIdx) } : null;

  // cross-market spreads — Chicago wheat vs corn with SELECTABLE contract months.
  const wheatContracts = board?.commodities?.wheat || [];
  const cornContracts = board?.commodities?.corn || [];
  const wLeg = wheatContracts.find(c => c.contract_month === wheatLeg) || wheatContracts[0];
  const cLeg = cornContracts.find(c => c.contract_month === cornLeg) || cornContracts[0];
  const wcSpread = (wLeg && cLeg) ? (wLeg.settle_price - cLeg.settle_price) : null;

  // COT weekly flow
  const cotHist = cotC?.history || [];
  const cotWoW = cotHist.length >= 2 ? cotHist[cotHist.length - 1].net - cotHist[cotHist.length - 2].net : null;

  const card = { background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, padding: '14px 16px', marginBottom: 12 };
  const capLbl = { fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#4a6e4a', textTransform: 'uppercase' };
  const stat = (label, value, sub, col) => (
    <div style={{ background: '#f9fcf6', border: '1px solid #e4ece4', borderRadius: 8, padding: '8px 12px', minWidth: 120, flex: '1 1 120px' }}>
      <div style={{ fontSize: 10, color: '#60735f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: col || '#1a2e1a' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#677167' }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['Corn', 'Soybeans', 'Wheat'].map(c => (
            <button key={c} onClick={() => setCrop(c)} style={{
              padding: '7px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              fontWeight: crop === c ? 800 : 600, border: '1px solid',
              background: crop === c ? GRAIN_COLORS[c] : '#fff', color: crop === c ? '#fff' : '#4a6e4a', borderColor: crop === c ? GRAIN_COLORS[c] : '#c8d8c0',
            }}>{GRAIN_TICKER[c]} · {c}</button>
          ))}
        </div>
        {board?.grain_as_of && <div style={{ fontSize: 10, color: '#2d6a1a', fontWeight: 700 }}>● Live · USDA settled {board.grain_as_of}</div>}
      </div>

      {/* Metrics strip (data only) + cost of carry */}
      <div style={card}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'stretch' }}>
          {stat('Nearby spread', consec[0] ? c1(consec[0].spread) : '—', consec[0] ? `${consec[0].from}→${consec[0].to} · ${consec[0].perMo.toFixed(1)}¢/mo` : '', consec[0] && consec[0].spread < 0 ? '#c0392b' : '#1a2e1a')}
          {stat('Nearby % full carry', consec[0]?.pct != null ? Math.round(consec[0].pct) + '%' : '—', 'spread ÷ cost-of-carry', consec[0] && consec[0].pct < 0 ? '#c0392b' : consec[0] && consec[0].pct >= 67 ? '#2d6a1a' : '#c8860a')}
          {stat('Old→new crop', oldNew ? c1(oldNew.spread) : '—', oldNew ? `${oldNew.from}→${oldNew.to}` : 'n/a', oldNew && oldNew.spread < 0 ? '#c0392b' : '#1a2e1a')}
          {stat('Front→back carry', frontToBack ? c1(frontToBack.spread) : '—', frontToBack ? `${frontToBack.m} mo · ${Math.round(frontToBack.pct)}% full` : '', '#1a2e1a')}
          {stat('Curve shape', curveState, `${curve.length} contracts`, curveState === 'inverse' ? '#c0392b' : curveState === 'carry' ? '#2d6a1a' : '#c8860a')}
          <div style={{ background: '#fff', border: '1px solid #e4ece4', borderRadius: 8, padding: '8px 12px', display: 'flex', gap: 10 }}>
            <label style={{ fontSize: 10, color: '#60735f', textTransform: 'uppercase' }}>Storage ¢/mo<br />
              <input type="number" step="0.5" value={storage} onChange={e => setStorage(e.target.value)} style={{ width: 58, marginTop: 3, padding: '4px 6px', border: '1px solid #c8d8c0', borderRadius: 6, fontVariantNumeric: "tabular-nums" }} />
            </label>
            <label style={{ fontSize: 10, color: '#60735f', textTransform: 'uppercase' }}>Interest %<br />
              <input type="number" step="0.5" value={interest} onChange={e => setInterest(e.target.value)} style={{ width: 52, marginTop: 3, padding: '4px 6px', border: '1px solid #c8d8c0', borderRadius: 6, fontVariantNumeric: "tabular-nums" }} />
            </label>
          </div>
        </div>
      </div>

      {/* Forward curve w/ full-carry overlay + carry matrix */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
        <div style={card}>
          <div style={capLbl}>{crop} Forward Curve vs. Full Carry</div>
          <div style={{ fontSize: 11, color: '#60735f', margin: '3px 0 6px' }}>Solid = settle. Dashed = front month + accumulated full carry. Gap = carry the market isn't paying.</div>
          <ElevatorCurveChart curve={curve} storage={storage} interest={interest} color={color} />
        </div>
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={capLbl}>Carry Matrix</div>
            <div style={{ display: 'flex', border: '1px solid #c8d8c0', borderRadius: 6, overflow: 'hidden' }}>
              {[['pct', '% carry'], ['cents', '¢ spread']].map(([m, l]) => (
                <button key={m} onClick={() => setMatrixMode(m)} style={{ padding: '3px 9px', fontSize: 10, fontWeight: matrixMode === m ? 800 : 600, border: 'none', cursor: 'pointer', background: matrixMode === m ? '#2d4a2d' : '#fff', color: matrixMode === m ? '#fff' : '#4a6e4a', fontFamily: 'inherit' }}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#60735f', margin: '3px 0 8px' }}>Store from <em>row</em> → <em>column</em>. <span style={{ color: '#2d6a1a', fontWeight: 700 }}>Green ≥100%</span> nets positive · <span style={{ color: '#7a8a2a', fontWeight: 700 }}>olive 67–100%</span> partial · <span style={{ color: '#c8860a', fontWeight: 700 }}>amber</span> weak · <span style={{ color: '#c0392b', fontWeight: 700 }}>red</span> inverse.</div>
          {curve.length >= 2 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
                <thead><tr>
                  <th style={{ padding: '4px', color: '#60735f', fontSize: 10, textAlign: 'left' }}>↓/→</th>
                  {curve.slice(1).map(c => <th key={c.contract_month} style={{ padding: '4px 2px', color: '#60735f', fontVariantNumeric: "tabular-nums", fontSize: 10 }}>{c.contract_month}</th>)}
                </tr></thead>
                <tbody>
                  {curve.slice(0, -1).map((ci, i) => (
                    <tr key={ci.contract_month}>
                      <td style={{ padding: '4px', color: '#60735f', fontVariantNumeric: "tabular-nums", fontSize: 10, whiteSpace: 'nowrap' }}>{ci.contract_month}</td>
                      {curve.slice(1).map((cj, jj) => {
                        const j = jj + 1;
                        if (j <= i) return <td key={cj.contract_month} style={{ background: '#fafafa' }} />;
                        const cell = carryCell(i, j);
                        return (
                          <td key={cj.contract_month} title={cell ? `${ci.contract_month}→${cj.contract_month}: ${c1(cell.spread)} (${cell.perMo.toFixed(1)}¢/mo), full carry ${cell.full.toFixed(1)}¢, ${Math.round(cell.pct)}%` : ''}
                            style={{ padding: '5px 2px', textAlign: 'center', fontVariantNumeric: "tabular-nums", fontWeight: 700, background: carryColor(cell?.pct), color: '#1a2e1a' }}>
                            {!cell ? '—' : matrixMode === 'pct' ? Math.round(cell.pct) + '%' : cell.spread.toFixed(0)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: '#aaa', fontSize: 12 }}>Curve unavailable.</div>}
        </div>
      </div>

      {/* Consecutive spreads + cross-market spreads */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
        <div style={card}>
          <div style={capLbl}>{crop} Calendar Spreads</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5, marginTop: 6 }}>
            <thead><tr>{['Spread', 'Mo', 'Spread ¢', 'Cost ¢', 'Net ¢', '¢/mo', '% carry'].map((h, k) => <th key={h} style={{ padding: '3px 3px', fontSize: 10, color: '#60735f', textTransform: 'uppercase', textAlign: k === 0 ? 'left' : 'right', borderBottom: '1px solid #dde8d8' }}>{h}</th>)}</tr></thead>
            <tbody>
              {consec.map((s, i) => {
                const net = s.spread - s.full; // actual return to a fully-hedged carry: spread earned − cost
                return (
                <tr key={i} style={{ borderTop: i ? '1px solid #f0f4ee' : 'none' }}>
                  <td style={{ padding: '3px 3px', color: '#4a6e4a', fontVariantNumeric: "tabular-nums" }}>{s.from}→{s.to}</td>
                  <td style={{ padding: '3px 3px', textAlign: 'right', color: '#677167', fontVariantNumeric: "tabular-nums" }}>{s.m}</td>
                  <td style={{ padding: '3px 3px', textAlign: 'right', fontVariantNumeric: "tabular-nums", color: s.spread < 0 ? '#c0392b' : '#1a2e1a' }}>{s.spread.toFixed(1)}</td>
                  <td style={{ padding: '3px 3px', textAlign: 'right', fontVariantNumeric: "tabular-nums", color: '#8a9a88' }}>{s.full.toFixed(1)}</td>
                  <td style={{ padding: '3px 3px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 800, color: net >= 0 ? '#2d6a1a' : '#c0392b' }}>{net >= 0 ? '+' : ''}{net.toFixed(1)}</td>
                  <td style={{ padding: '3px 3px', textAlign: 'right', fontVariantNumeric: "tabular-nums", color: '#60735f' }}>{s.perMo.toFixed(1)}</td>
                  <td style={{ padding: '3px 3px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: s.pct < 0 ? '#c0392b' : s.pct >= 100 ? '#2d6a1a' : s.pct >= 67 ? '#7a8a2a' : '#c8860a' }}>{Math.round(s.pct)}%</td>
                </tr>
              ); })}
            </tbody>
          </table>
          <div style={{ fontSize: 10, color: '#677167', marginTop: 6, lineHeight: 1.5 }}>Cost ¢ = full carry (storage {storage}¢/mo + {interest}% interest). <strong>Net ¢ = spread − cost</strong>: <span style={{ color: '#2d6a1a' }}>+ pays to carry hedged</span>, <span style={{ color: '#c0392b' }}>− it costs you</span>. Only ≥100% carry nets positive on full cost — but elevators often still store at 67–100% (marginal storage &lt; full commercial rate + basis usually firms).</div>
        </div>
        <div style={card}>
          <div style={capLbl}>Cross-Market Spreads</div>
          {/* Corn/Soy ratio (new-crop) */}
          {ratio && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderBottom: '1px solid #f0f4ee' }}>
              <div><div style={{ fontWeight: 700, color: '#1a2e1a' }}>Corn / Soy ratio</div><div style={{ fontSize: 10, color: '#677167' }}>{ratio.corn_contract} corn ÷ {ratio.soy_contract} beans</div></div>
              <div style={{ fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 15, color: '#1a4a7a' }}>{Number(ratio.value).toFixed(2)}</div>
            </div>
          )}
          {/* Chicago Wheat − Corn with selectable contract months on each leg */}
          <div style={{ padding: '10px 0' }}>
            <div style={{ fontWeight: 700, color: '#1a2e1a', marginBottom: 6 }}>Chicago Wheat − Corn</div>
            {wheatContracts.length && cornContracts.length ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <select value={wLeg?.contract_month || ''} onChange={e => setWheatLeg(e.target.value)}
                    style={{ padding: '5px 7px', border: '1px solid #c8d8c0', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', background: '#fff' }}>
                    {wheatContracts.map(c => <option key={c.contract_month} value={c.contract_month}>Wheat {c.contract_month} · {money(c.settle_price)}</option>)}
                  </select>
                  <span style={{ color: '#60735f', fontWeight: 700 }}>−</span>
                  <select value={cLeg?.contract_month || ''} onChange={e => setCornLeg(e.target.value)}
                    style={{ padding: '5px 7px', border: '1px solid #c8d8c0', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', background: '#fff' }}>
                    {cornContracts.map(c => <option key={c.contract_month} value={c.contract_month}>Corn {c.contract_month} · {money(c.settle_price)}</option>)}
                  </select>
                  <span style={{ marginLeft: 'auto', fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 18, color: wcSpread < 0 ? '#c0392b' : '#1a4a7a' }}>{wcSpread != null ? money(wcSpread) : '—'}</span>
                </div>
                <div style={{ fontSize: 10, color: '#677167', marginTop: 5 }}>
                  Wheat premium over corn ($/bu){wLeg && cLeg ? ` · ${wLeg.contract_month} wheat ${money(wLeg.settle_price)} − ${cLeg.contract_month} corn ${money(cLeg.settle_price)}` : ''}
                </div>
              </>
            ) : <div style={{ fontSize: 11, color: '#aaa' }}>Curve unavailable.</div>}
          </div>
          <div style={{ fontSize: 10, color: '#677167', marginTop: 4 }}>Board crush (meal+oil−beans) needs soymeal/soyoil futures — not in the USDA feed; would require a licensed data source.</div>
        </div>
      </div>

      {/* Fund positioning + basis */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={capLbl}>{crop} Fund Positioning (managed money)</div>
            {cotC && <span style={{ fontSize: 10, color: '#677167' }}>CFTC {cotC.report_date}</span>}
          </div>
          {cotC ? (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: cotC.mm_net >= 0 ? '#3a6e32' : '#c0392b' }}>{cotC.mm_net >= 0 ? '+' : ''}{Number(cotC.mm_net).toLocaleString()}</span>
                <span style={{ fontSize: 11, color: '#60735f' }}>net {cotC.mm_net >= 0 ? 'long' : 'short'}</span>
                {cotWoW != null && <span style={{ fontSize: 12, fontWeight: 700, color: cotWoW >= 0 ? '#3a6e32' : '#c0392b' }}>{cotWoW >= 0 ? '▲ +' : '▼ '}{Number(cotWoW).toLocaleString()} wk/wk</span>}
              </div>
              <div style={{ fontSize: 10, color: '#677167', marginTop: 2 }}>{Number(cotC.mm_long).toLocaleString()} long / {Number(cotC.mm_short).toLocaleString()} short</div>
              {cotC.wk52_pct != null && (
                <>
                  <div style={{ position: 'relative', height: 9, background: '#eef2ec', borderRadius: 5, margin: '10px 0 4px' }}>
                    <div style={{ position: 'absolute', left: `calc(${(cotC.wk52_pct * 100).toFixed(0)}% - 5px)`, top: -3, width: 10, height: 10, borderRadius: 5, background: cotC.wk52_pct > 0.8 ? '#c0392b' : cotC.wk52_pct < 0.2 ? '#3a6e32' : '#60735f', border: '2px solid #fff', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
                  </div>
                  <div style={{ fontSize: 10, color: '#677167', textAlign: 'center' }}>{Math.round(cotC.wk52_pct * 100)}%ile of 52-wk net-position range</div>
                </>
              )}
              <GrainTinyChart points={cotHist.map(h => ({ y: h.net }))} color="#4a6e4a" height={60} zeroLine fill={false} />
            </div>
          ) : <div style={{ color: '#aaa', fontSize: 12, marginTop: 6 }}>Positioning unavailable.</div>}
        </div>
        <div style={card}>
          <div style={capLbl}>Local Cash Bids — posted boards</div>
          {[['WNY Energy · Corn', cornBoard], ['Star of the West · Wheat', wheatBoard]].map(([title, brd]) => (
            <div key={title} style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#1a2e1a', marginBottom: 3 }}>
                {title}{brd && brd.length ? <span style={{ fontWeight: 400, color: '#677167', fontSize: 10 }}> · {brd[0].bid_date}</span> : null}
              </div>
              {brd == null ? <div style={{ fontSize: 11, color: '#aaa' }}>Loading…</div>
                : brd.length ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead><tr>{['Delivery', 'Bid', 'Futures', 'Basis'].map((h, k) => (
                      <th key={h} style={{ padding: '3px 4px', fontSize: 10, color: '#60735f', textTransform: 'uppercase', textAlign: k === 0 ? 'left' : 'right', borderBottom: '1px solid #dde8d8' }}>{h}</th>
                    ))}</tr></thead>
                    <tbody>
                      {brd.map((r, i) => (
                        <tr key={i} style={{ borderTop: i ? '1px solid #f0f4ee' : 'none' }}>
                          <td style={{ padding: '3px 4px', color: '#4a6e4a', fontVariantNumeric: "tabular-nums" }}>{r.delivery_period}</td>
                          <td style={{ padding: '3px 4px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: '#1a2e1a' }}>{money(r.cash_price)}</td>
                          <td style={{ padding: '3px 4px', textAlign: 'right', fontVariantNumeric: "tabular-nums", color: '#60735f' }}>{String(r.futures_month || '').replace(/ (Corn|Wheat|Soybeans)$/i, '')}{r.futures_price != null ? ' ' + money(r.futures_price) : ''}</td>
                          <td style={{ padding: '3px 4px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: '#1a4a7a' }}>{r.published_basis != null ? (Number(r.published_basis) >= 0 ? '+' : '') + Number(r.published_basis).toFixed(2) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div style={{ fontSize: 11, color: '#aaa' }}>no bids posted</div>}
            </div>
          ))}
          <div style={{ fontSize: 10, color: '#677167', marginTop: 8 }}>Posted cash bid, the futures month it's quoted against, and basis ($/bu). WNY Energy (Medina) · Star of the West (Churchville).</div>
        </div>
      </div>
    </div>
  );
}

// ── Grain Cash Board ─────────────────────────────────────────────────────────
// Howlett's own posted basis, imported from a photo of the printed basis sheet
// (via /api/extract vision) into grain_basis_board, then shown as a live cash
// board (cash = basis + USDA futures, recomputed on read). NOT part of the WNY
// Basis Pulse — that's competitors only; this is Howlett's own book, feeding the
// grain-marketing spots + forward curve. Each sheet column = a (location, term).
const CME_MONTH = { F:'Jan', G:'Feb', H:'Mar', J:'Apr', K:'May', M:'Jun', N:'Jul', Q:'Aug', U:'Sep', V:'Oct', X:'Nov', Z:'Dec' };
function cmeToContractMonth(code){
  const m = String(code||'').toUpperCase().match(/^([FGHJKMNQUVXZ])(\d{2})$/);
  return m ? `${CME_MONTH[m[1]]} ${m[2]}` : null;
}
const BOARD_COMMODITY_TO_CROP = { corn:'Corn', soybeans:'Soybeans', plenish:'Soybeans', wheat:'Wheat' };
const BOARD_COMMODITY_ORDER = ['corn','soybeans','plenish','wheat'];
const boardCommodityLabel = c => ({ corn:'Corn', soybeans:'Soybeans', plenish:'Plenish', wheat:'Wheat' }[c] || c);
const termForLocation = loc => /p\/u|pick/i.test(String(loc||'')) ? 'pickup' : 'delivered';
const cashFmt = n => (n==null||isNaN(n)) ? '—' : `$${Number(n).toFixed(2)}`;
const basisFmt = n => (n==null||isNaN(n)) ? '—' : `${n<0?'−':'+'}${Math.abs(Number(n)).toFixed(2)}`;
// The 3-way scale opportunityCategory() already defines, given colours once, so
// "strong" reads the same whichever gauge a reader is looking at.
const STRENGTH_COLOR = { strong:'#2d6a1a', normal:'#60735f', weak:'#c0392b', unknown:'#b9c9b9' };
// Phone photos are several MB — over Vercel's request body limit (413 "Request
// Entity Too Large") and Anthropic's image cap. Downscale in-browser to a long
// edge Anthropic won't further shrink, as JPEG, before sending.
// The upload has to survive a bad connection, because we cannot fix one from
// here. A basis-sheet import failed twice on 2026-08-24/25 with the browser's
// bare network error — no HTTP response at all — while production accepted
// every payload thrown at it from this machine: 365 KB, 971 KB and 2,439 KB
// all returned 200 in 2-3s, on the real prompt with a real photo. So the
// failure is on the wire, not at the service, and the answer is to send less
// and to retry rather than to keep guessing at a cause.
//
// MAX_UPLOAD_B64_KB is a budget, not a quality setting. A 1568px photo of a
// printed table lands around 250 KB at q0.85; Kevin's was 603 KB, so a denser
// sheet can more than double it. Stepping the quality down keeps printed digits
// legible (they are high-contrast black on white) while bounding the request.
const MAX_UPLOAD_B64_KB = 420;
const UPLOAD_QUALITY_STEPS = [0.85, 0.72, 0.6];
const b64Kb = (b64) => Math.round((b64.length * 3) / 4 / 1024);

function downscaleImage(file, maxEdge = 1568, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      try {
        // Step the quality down until the payload fits the budget. The LAST
        // step is used even if it is still over — a large upload is better
        // than no upload, and the caller reports the size either way.
        let data = null;
        for (const q of (quality === 0.85 ? UPLOAD_QUALITY_STEPS : [quality])) {
          data = canvas.toDataURL('image/jpeg', q).split(',')[1];
          if (b64Kb(data) <= MAX_UPLOAD_B64_KB) break;
        }
        resolve({ data, mime: 'image/jpeg' });
      }
      catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('could not read the image file')); };
    img.src = url;
  });
}

// ── archiveSheetObservations — write an imported sheet into the permanent
// history (basis_sheet_observations, sql/035), keyed the same way the
// 2021-2026 backfill is so a new sheet EXTENDS the history rather than
// starting a cohort beside it.
//
// THE SLUG IS THE WHOLE POINT. basisStrength.js gates comparability on the
// source slug, and this sheet spells one delivery point several ways across its
// commodity sections — "Avon & Lakeville" in soybeans, "Avon & Lkville" in
// corn, "Avon/Lakevlle" in wheat. A generic slugger preserves each spelling,
// which is why basis_calculated_daily had carried Avon/Lakeville as three
// separate sources. slugForMarket() is the one shared map that stops that here.
//
// An unrecognized column is filed under an obviously-unmapped slug, never
// guessed into the nearest known elevator — a wrong merge silently corrupts a
// real elevator's history, while an unmapped source is visible and fixable.
async function archiveSheetObservations(boardRows, review) {
  // The sheet's own UPDATE stamp is the observation date — a sheet photographed
  // days later is still that day's board. Fall back to today only when the
  // sheet printed none, and SAY which, rather than leaving the reader to guess.
  const parsed = review?.updated ? new Date(review.updated) : null;
  const stampOk = parsed && !isNaN(parsed.getTime());
  const sheetDate = stampOk
    ? `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
    : new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const dateNote = stampOk
    ? `sheet UPDATE stamp ${review.updated}`
    : 'sheet printed no UPDATE stamp - dated by import day (America/New_York)';

  // ── keep the photograph ────────────────────────────────────────────────
  // Without it, a cell that reads oddly later can never be checked against what
  // was actually printed. Keyed on the sheet's own date and version with
  // x-upsert, so re-importing a sheet REPLACES its image rather than piling up
  // near-duplicates. A failed upload records source_image:null and SAYS SO —
  // the rows are what cannot be recovered later; the sheet can be re-shot.
  let imagePath = null, imageNote = '';
  if (review?.file) {
    const ext = (review.file.name || '').match(/\.(jpe?g|png|heic|webp)$/i)?.[1] || 'jpg';
    const safeVersion = String(review.version || 'nover').replace(/[^A-Za-z0-9_-]/g, '');
    const candidate = `${sheetDate.slice(0, 4)}/${sheetDate}__v${safeVersion}.${ext.toLowerCase()}`;
    try {
      await sbStorageUpload('basis-sheets', candidate, review.file);
      imagePath = candidate;
      imageNote = ` - photo kept at basis-sheets/${candidate}`;
    } catch (e) {
      imageNote = ` - PHOTO NOT RETAINED (upload failed: ${e.message})`;
    }
  } else {
    imageNote = ' - no photo carried through this import';
  }

  // The board's priced cells PLUS the sheet's refusals. The table constrains
  // exactly one of (no_bid, basis), so a NO_BID carries a null basis and can
  // never be averaged into a percentile — but it IS recorded, because "Geneva
  // would not bid Dec wheat" is a fact and the archive holds 846 of them.
  const priced = boardRows.map(r => ({ ...r, noBid: false }));
  const refused = (review?.noBidRows || []).map(r => ({
    commodity: r.commodity, location: r.location, term: r.term,
    cme_contract: r.cme_contract, ship_month: r.ship_month, basis: null, noBid: true,
  }));
  const rows = [...priced, ...refused].map(r => ({
    sheet_date: sheetDate,
    // PLENISH is a PROGRAM, not a commodity — a contracted specialty soybean.
    // The board tracks it separately so it renders in its own block; the
    // history stores it as soybeans carrying a plenish_* slug, which is how the
    // backfill stores it and what keeps a Plenish premium out of a standard
    // soybean percentile.
    commodity: r.commodity === 'plenish' ? 'soybeans' : r.commodity,
    // The board has no crop-year column. NULL means the sheet did not say it;
    // deriving one from the contract month would be an inference.
    crop_year: null,
    source: slugForMarket(r.location) || unmappedSlug(r.location),
    source_label: r.location,
    bid_structure: r.term,
    delivery_window: r.ship_month,
    futures_contract: r.cme_contract || '',
    // Exactly one of these is set — the table CHECKs it. A NO_BID must never
    // arrive as 0.00: that reads as "bid flat", the opposite of "would not bid".
    basis: r.noBid ? null : r.basis,
    no_bid: !!r.noBid,
    confidence: 'vision_extracted_reviewed',
    provenance: `Howlett basis sheet imported through Grain Marketing - ${dateNote}`
      + `${review?.version ? ` - sheet Version ${review.version}` : ''}`
      + ` - read by claude vision from a photograph and reviewed on screen before saving`
      + imageNote,
    source_image: imagePath,
    sheet_version: review?.version || null,
    sheet_updated: review?.updated || null,
    import_batch: `sheet-import-${sheetDate}`,
  }));
  if (!rows.length) return;
  await sbUpsert('basis_sheet_observations', rows,
    'sheet_date,commodity,source,bid_structure,delivery_window,futures_contract');
}

function GrainCashBoard({ liveFutures, isMobile, portal = false, adminSwitch = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [review, setReview] = useState(null);            // { version, updated, rows:[...] }
  const [termFilter, setTermFilter] = useState(portal ? 'delivered' : 'all');   // all | pickup | delivered
  const [viewMode, setViewMode] = useState('cash');      // cash | basis
  const [portalLoc, setPortalLoc] = useState('');        // portal: the single location the farmer is viewing
  const [portalCrop, setPortalCrop] = useState('');      // portal: the crop the farmer is viewing (locations differ by crop)
  const fileRef = React.useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    sbFetch('grain_basis_board', { order: 'commodity.asc,ship_sort.asc' })
      .then(r => setRows(r || [])).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── historical basis, for the strength reading ─────────────────────────
  // 6,377 transcribed observations across 44 sheets and five crop years. BID
  // rows only — a NO_BID is a market state that can never enter a distribution,
  // so fetching them would just be weight.
  //
  // NOT FETCHED IN PORTAL. The chips render only in the agency matrix below, so
  // a portal session would pull the whole archive to display none of it — page
  // weight on the surface this app has already crashed once for being too
  // heavy. Gating the fetch (not just the render) also keeps this read off the
  // portal's reachable surface.
  //
  // Errors are HELD, not swallowed: a failed fetch must read as "the archive
  // did not load", never as "this basis has no history" — opposite facts.
  const [history, setHistory] = useState(null);
  const [histErr, setHistErr] = useState(null);
  useEffect(() => {
    if (portal) return;
    // The NO_BID exclusion goes in `filters`, NOT as a bare option key —
    // sbFetch destructures a fixed set and silently ignores anything else, the
    // same trap its own header documents for `limit`.
    sbFetch('basis_sheet_observations', {
      select: 'commodity,source,bid_structure,futures_contract,sheet_date,basis,no_bid',
      filters: ['no_bid=is.false'],
    })
      .then(r => setHistory((r || []).map(normalizeObservation).filter(Boolean)))
      .catch(e => { setHistErr(e.message); setHistory([]); });
  }, [portal]);

  // ── futures history, for the other half of the reading ─────────────────
  const [futHist, setFutHist] = useState(null);
  useEffect(() => {
    if (portal) return;
    sbFetch('futures_contract_history', {
      select: 'commodity,contract_sym,bar_date,high,low,close',
      order: 'bar_date.asc',
    })
      .then(r => setFutHist(r || []))
      .catch(() => setFutHist([]));
  }, [portal]);

  // contract -> gauge. Keyed per (commodity, contract) rather than per cell:
  // every location in a delivery row is priced off the SAME futures contract,
  // so the futures reading belongs to the row and the basis reading to the
  // cell. That split is the point — a cash price is futures plus basis, and the
  // two can be strong and weak independently.
  const futStrengthByContract = useMemo(() => {
    if (!futHist || !futHist.length || !rows.length) return {};
    const PREFIX = { corn: 'C', soybeans: 'S', wheat: 'W' };
    const normalized = normalizeFuturesHistoryRows(futHist.map(r => ({
      crop: r.commodity,
      contractMonth: r.contract_sym.slice(1, 2),
      contractYear: Number('20' + r.contract_sym.slice(-2)),
      obsDate: r.bar_date, settle: r.close, high: r.high, low: r.low,
      source: 'futures_contract_history',
    }))).rows;
    const out = {};
    for (const r of rows) {
      const crop = r.commodity === 'plenish' ? 'soybeans' : (r.commodity === 'soy' ? 'soybeans' : r.commodity);
      const pfx = PREFIX[crop];
      const m = String(r.cme_contract || '').match(/^([FGHJKMNQUVXZ])(\d{2})$/);
      if (!pfx || !m) continue;
      const key = `${crop}|${r.cme_contract}`;
      if (out[key] !== undefined) continue;
      const year = 2000 + Number(m[2]);
      // The target is that contract's OWN latest settle in the archive, never
      // the board's basis — a futures percentile must be scored on futures.
      const own = normalized.filter(x => x.crop === crop && x.contractMonth === m[1] && x.contractYear === year);
      if (!own.length) { out[key] = null; continue; }
      const latest = own.reduce((a, b) => (a.obsDate > b.obsDate ? a : b));
      const score = scoreContractAtLifePoint({
        crop, contractMonth: m[1], targetContractYear: year,
        targetOffsetDays: contractLifeOffsetDays({ contractMonth: m[1], contractYear: year, obsDate: latest.obsDate }),
        targetSettle: latest.settle, history: normalized,
      });
      out[key] = score.available
        ? { ...buildContractLifeFuturesGauge(score, `${pfx}${m[1]}${m[2]}`), asOf: latest.obsDate, settle: latest.settle }
        : null;
    }
    return out;
  }, [futHist, rows]);

  // cellKey -> gauge, for every board cell the archive can defensibly score.
  // Keyed identically to matrixFor()'s own cell map so a lookup cannot drift
  // from the cell it decorates.
  const strengthByCell = useMemo(() => {
    if (!history || !history.length || !rows.length) return {};
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const out = {};
    for (const r of rows) {
      if (r.basis == null) continue;
      // PLENISH is stored in the history as soybeans on a plenish_* slug — the
      // same normalization the archive used, or the lookup silently finds none.
      const com = r.commodity === 'plenish' ? 'soybeans' : (r.commodity === 'soy' ? 'soybeans' : r.commodity);
      const score = scoreBasisAtLead({
        commodity: com,
        source: slugForMarket(r.location) || unmappedSlug(r.location),
        bidStructure: r.term, contract: r.cme_contract,
        obsDate: today, targetBasis: Number(r.basis), history,
      });
      if (score.available) {
        out[`${r.commodity}@@${r.cme_contract}|${r.ship_month}@@${r.location}`] = buildBasisStrengthGauge(score, r.location);
      }
    }
    return out;
  }, [history, rows]);

  function settleFor(commodity, cme){
    const cm = cmeToContractMonth(cme);
    const key = GRAIN_CROP_TO_COMMODITY[BOARD_COMMODITY_TO_CROP[commodity]];
    const arr = liveFutures?.commodities?.[key];
    if (!arr || !cm) return null;
    const hit = arr.find(r => r.contract_month === cm);
    return hit && hit.settle_price != null ? Number(hit.settle_price) : null;
  }
  const cashOf = (commodity, cme, basis) => {
    const f = settleFor(commodity, cme);
    return (f == null || basis == null) ? null : f + Number(basis);
  };

  async function onFile(e){
    const file = e.target.files?.[0]; if (!file) return;
    setErr(null); setBusy(true);
    let payloadKb = null;
    try {
      const { data: b64, mime: media } = await downscaleImage(file, 1568, 0.85);
      payloadKb = Math.round((b64.length * 3) / 4 / 1024);
      // ONE TRANSPORT, TWO READERS. The retry ladder, the thrown-vs-HTTP
      // distinction and the JSON slice moved to photoExtract.js so the
      // handwritten-ticket reader shares them rather than growing a second
      // copy — every line of them is a measured failure someone paid for.
      const parsed = await extractPhotoJson({
        b64, media, prompt: basisSheetPrompt(), apiFetch: adminApiFetch, onStatus: setErr,
      });
      // A NO_BID is read and KEPT, not skipped. "This buyer would not bid this
      // window" is a fact about the market — the transcribed 2021-2026 archive
      // carries 846 of them — and skipping it makes a refusal indistinguishable
      // from a window nobody asked about. Emitted as a marker, never a number,
      // so it can never be averaged: a NO_BID is a state, never 0.00.
      const NO_BID_RE = /^\s*(no[\s_-]?bid|nb)\s*$/i;
      const allCells = (parsed.rows || []).map(r => {
        // Compact array form [commodity, cme, ship, location, basis]; tolerate object form too.
        const [commodity, cme_contract, ship_month, location, basis] = Array.isArray(r)
          ? r : [r.commodity, r.cme_contract, r.ship_month, r.location, r.basis];
        return {
          commodity: String(commodity||'').toLowerCase().trim(),
          location: String(location||'').trim(),
          term: termForLocation(location),
          cme_contract: String(cme_contract||'').toUpperCase().trim(),
          ship_month: String(ship_month||'').trim(),
          noBid: typeof basis === 'string' && NO_BID_RE.test(basis),
          basis: (typeof basis === 'string' && NO_BID_RE.test(basis)) ? null : (basis==null ? null : Number(basis)),
        };
      }).filter(r => r.commodity && r.location && r.ship_month);
      // The BOARD carries priced bids only — grain_basis_board feeds the live
      // cash display and a NO_BID has no cash price. The archive gets both.
      const rv = allCells.filter(r => !r.noBid && r.basis != null && !isNaN(r.basis));
      const noBidCells = allCells.filter(r => r.noBid);
      // ship_sort = first-seen order of (cme|ship) within each commodity
      const seq = {};
      rv.forEach(r => { const k = r.commodity, key = r.cme_contract + '|' + r.ship_month; seq[k] = seq[k] || []; if (!seq[k].includes(key)) seq[k].push(key); });
      rv.forEach(r => { r.ship_sort = seq[r.commodity].indexOf(r.cme_contract + '|' + r.ship_month); });
      if (!rv.length) throw new Error('no basis cells read — retake the photo straight-on and in focus');
      // Carry the ORIGINAL file to save — not the downscale sent to the model:
      // the point of keeping it is to re-read a cell the model may have got
      // wrong, and the 1568px copy is the version that produced the reading
      // being questioned. Uploaded at SAVE because the sheet's own date names
      // the file and is only known after extraction.
      // What the board carries today that this sheet did NOT read back.
      const seen = new Set(allCells.map(r =>
        [r.commodity, r.location, r.cme_contract, r.ship_month].join('|')));
      const vanished = (rows || [])
        .filter(r => !seen.has([r.commodity, r.location, r.cme_contract, r.ship_month].join('|')))
        .map(r => ({ commodity: r.commodity, location: r.location,
                     cme_contract: r.cme_contract, ship_month: r.ship_month, basis: r.basis }));
      setReview({ version: parsed.version || '', updated: parsed.updated || '', rows: rv,
                  noBidRows: noBidCells, vanished, file });
    } catch (ex) {
      // A THROWN fetch and a REFUSED request are different problems and used to
      // print the same one-line message. "Load failed" / "Failed to fetch" is
      // the browser saying no HTTP response came back at all — a timeout, a
      // dropped connection or a body the edge would not take — not a rejection
      // from the service. Naming that, with the payload size, is the difference
      // between a diagnosis and a guess. Same argument as usePortalEmptyState
      // taking the view's error as a REQUIRED argument.
      const noResponse = ex instanceof TypeError ||
        /load failed|failed to fetch|networkerror|network request failed/i.test(ex.message || '');
      setErr('Extraction failed: ' + ex.message + (noResponse
        ? ' — no HTTP response after ' + (ex._tries || 1) + ' attempt' + ((ex._tries || 1) > 1 ? 's' : '')
          + '. The photo sent was ' + (typeof payloadKb === 'number' ? payloadKb + ' KB' : 'of unknown size')
          + '. The service itself is reachable, so this is the connection between here and it:'
          + ' check the network, then retake or retry. Nothing was saved.'
        : ''));
    }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  async function saveReview(){
    if (!review?.rows?.length) return;
    setBusy(true); setErr(null);
    try {
      const now = new Date().toISOString();
      // Dedupe by the unique key (commodity, location, ship_month, cme_contract) —
      // Postgres rejects an upsert whose batch hits the same conflict row twice.
      const byKey = new Map();
      review.rows.filter(r => r.basis != null && !isNaN(r.basis)).forEach(r => {
        byKey.set(`${r.commodity}|${r.location}|${r.ship_month}|${r.cme_contract}`, {
          commodity:r.commodity, location:r.location, term:r.term, cme_contract:r.cme_contract,
          ship_month:r.ship_month, ship_sort:r.ship_sort, basis:Number(r.basis), sheet_version:review.version||null, sheet_updated:review.updated||null, updated_at:now,
        });
      });
      const payload = [...byKey.values()];
      // Replace, don't accumulate: an import IS the full board for the commodities
      // it covers. Delete those commodities' existing rows first, so stale rows
      // whose location name changed between sheets (a mis-merged column, or a
      // Plenish column that slipped under soybeans last time) don't linger.
      const commods = [...new Set(payload.map(r => r.commodity))];
      if (commods.length) await sbDelete('grain_basis_board', `commodity=in.(${commods.join(',')})`);
      await sbInsert('grain_basis_board', payload);

      // ── KEEP THE SHEET. Everything above REPLACES the board — the delete/
      // insert pair means grain_basis_board holds the newest sheet and nothing
      // else, so before this every imported sheet was erased by the next one.
      // The only history that survived was api/_howlett-basis.js's daily
      // snapshot, which begins 2026-07-09.
      //
      // basis_sheet_observations (sql/035) is APPEND-ONLY by construction:
      // upsert on the observation key, no delete anywhere. Re-importing the
      // same sheet converges instead of duplicating.
      //
      // A FAILURE HERE MUST NOT FAIL THE IMPORT. The board write above already
      // succeeded and is what the screen needs; losing the archive copy is
      // worth reporting, not worth telling Kevin his sheet did not save.
      try { await archiveSheetObservations(payload, review); }
      catch (ax) { setErr(`Board saved. Sheet archive did NOT save: ${ax.message}`); }
      setReview(null); load();
    } catch (ex) { setErr('Save failed: ' + ex.message); }
    finally { setBusy(false); }
  }

  // Build a matrix for one commodity from a set of rows: ordered deliveries × locations.
  function matrixFor(commodity, src){
    const mine = src.filter(r => r.commodity === commodity);
    const locSet = [];
    mine.forEach(r => { if (!locSet.includes(r.location)) locSet.push(r.location); });
    const locs = locSet.filter(l => termFilter === 'all' || termForLocation(l) === termFilter);
    const delMap = {};
    mine.forEach(r => { const k = r.cme_contract + '|' + r.ship_month; if (!delMap[k]) delMap[k] = { cme:r.cme_contract, ship:r.ship_month, sort:r.ship_sort }; });
    const dels = Object.values(delMap).sort((a,b) => (a.sort||0) - (b.sort||0));
    const cell = {}; mine.forEach(r => { cell[(r.cme_contract+'|'+r.ship_month)+'@@'+r.location] = r; });
    return { locs, dels, cell };
  }

  const card = { background:'#fff', border:'1px solid #dde7dd', borderRadius:10, padding:16, marginBottom:16 };
  const th = { padding:'6px 8px', textAlign:'right', fontSize:10, color:'#60735f', textTransform:'uppercase', letterSpacing:.3, borderBottom:'2px solid #e3ece3', whiteSpace:'nowrap' };
  const td = { padding:'6px 8px', textAlign:'right', fontSize:13, borderBottom:'1px solid #f0f4f0', whiteSpace:'nowrap' };
  const btn = (on) => ({ padding:'6px 12px', borderRadius:7, fontSize:12, fontWeight:700, cursor:'pointer', border:'1px solid', background:on?'#2d4a2d':'#fff', color:on?'#fff':'#4a6e4a', borderColor:on?'#2d4a2d':'#c8d8c0', fontFamily:'inherit' });

  const version = rows[0]?.sheet_version;
  const sheetUpdated = rows[0]?.sheet_updated;
  const commodsPresent = BOARD_COMMODITY_ORDER.filter(c => rows.some(r => r.commodity === c));

  // Portal farmers don't get the full all-locations sheet — they pick a crop, a
  // term (delivered / picked up), and one location, and see just that slice.
  // Location labels differ by crop on the board (corn "Avon & Lakeville" vs wheat
  // "Avon/Lkvlle/Star"), so locations are scoped to the chosen crop.
  const effCrop = portal ? (commodsPresent.includes(portalCrop) ? portalCrop : (commodsPresent[0] || '')) : '';
  const portalLocs = portal
    ? rows.reduce((acc, r) => { if (r.commodity === effCrop && termForLocation(r.location) === termFilter && !acc.includes(r.location)) acc.push(r.location); return acc; }, [])
    : [];
  const effLoc = portal ? (portalLocs.includes(portalLoc) ? portalLoc : (portalLocs[0] || '')) : '';

  return (
    <div>
      {/* Admin-only visibility switch (app_config.cash_board_enabled). Rendered
          only for Kevin / Mike; everyone else either sees the board or has no tab. */}
      {adminSwitch && (
        <div style={{ ...card, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10,
                      background: adminSwitch.on === false ? '#fdf3e2' : '#f2f6f2',
                      border: `1px solid ${adminSwitch.on === false ? '#e8c98a' : '#cfe4c4'}` }}>
          <div style={{ fontSize:13, color:'#1a2e1a' }}>
            <strong>Cash Board is {adminSwitch.on === null ? '…' : adminSwitch.on ? 'ON' : 'OFF'}</strong>
            <span style={{ color:'#5a7a4a' }}>
              {adminSwitch.on === false
                ? ' — hidden from customers and staff. Only admins can see this page.'
                : ' — visible to everyone with Grain Marketing, including portal customers.'}
            </span>
          </div>
          <button disabled={adminSwitch.saving || adminSwitch.on === null}
            onClick={() => {
              const next = !adminSwitch.on;
              if (window.confirm(next ? 'Turn the Cash Board ON? Posted cash bids will be visible to portal customers.'
                                      : 'Turn the Cash Board OFF? It will be hidden from everyone except admins.')) adminSwitch.onToggle(next);
            }}
            style={{ padding:'7px 16px', borderRadius:20, border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:800, fontSize:13,
                     background: adminSwitch.on ? '#2d4a2d' : '#c0392b', color:'#fff', opacity: adminSwitch.saving ? 0.6 : 1 }}>
            {adminSwitch.saving ? 'Saving…' : adminSwitch.on ? 'Turn off' : 'Turn on'}
          </button>
        </div>
      )}
      {/* Header + import */}
      <div style={{ ...card, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:800, color:'#1a2e1a' }}>🌾 Cash Board <span style={{ fontSize:12, fontWeight:600, color:'#60735f' }}>— your posted basis → live cash</span></div>
          {(version || sheetUpdated) && (
            <div style={{ display:'inline-block', marginTop:4, background:'#eaf4e4', border:'1px solid #cfe4c4', borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:700, color:'#2d5a10' }}>
              📄 {version ? `Version ${version}` : 'Basis sheet'}{sheetUpdated ? ` · dated ${sheetUpdated}` : ''}
              <span style={{ fontWeight:500, color:'#5a7a4a' }}> — check against your latest sheet</span>
            </div>
          )}
          <div style={{ fontSize:12, color:'#60735f', marginTop:4 }}>
            Cash = basis + USDA futures (as of {liveFutures?.grain_as_of || '—'}). Update by photographing a new basis sheet.
          </div>
          <div style={{ fontSize:11, color:'#b08a2a', marginTop:3, fontWeight:600 }}>
            ⚠ Cash reflects the daily futures <strong>settlement</strong> — updated once per day after the close (~5 PM ET), not intraday.
          </div>
          {/* Strength legend. States coverage plainly: a cell with no percentile
              has too little comparable history, which is a different statement
              from "average" and must not be read as one. */}
          {history && (
            <div style={{ fontSize:11, color:'#60735f', marginTop:5 }}>
              {histErr
                ? <span style={{ color:'#c0392b', fontWeight:600 }}>Basis strength unavailable — the history archive did not load ({histErr}). That is not a statement about these bids.</span>
                : <details>
                    {/* The count stays visible; the method is one click away instead of
                        seven lines sitting in front of the prices. */}
                    <summary style={{ cursor:'pointer', fontWeight:600 }}>
                      <strong style={{ color:'#1a2e1a' }}>{Object.keys(strengthByCell).length}</strong> of {rows.length} cells carry a basis-strength percentile · <span style={{ textDecoration:'underline' }}>how this works</span>
                    </summary>
                    <div style={{ marginTop:4 }}>
                    {' '}(<span style={{ color:STRENGTH_COLOR.weak, fontWeight:800 }}>weak</span> ≤33 ·
                    {' '}<span style={{ color:STRENGTH_COLOR.normal, fontWeight:800 }}>normal</span> ·
                    {' '}<span style={{ color:STRENGTH_COLOR.strong, fontWeight:800 }}>strong</span> ≥67),
                    {' '}scored against {history.length.toLocaleString()} archived observations from the same delivery point and structure, same delivery month, a similar distance from delivery, in <em>other</em> crop years — each year weighted equally so a heavily-photographed year cannot dominate.
                    {' '}A cell with no percentile has too little comparable history to score; that is not "average".
                    {' '}<strong style={{ color:'#1a2e1a' }}>fut</strong> on the delivery row is the same reading for the FUTURES contract, against other years at the same point in contract life. Basis and futures move independently, so a strong board can sit on a weak basis or the reverse; they are shown apart rather than blended. Hover any percentile for its sample.
                    </div>
                  </details>}
            </div>
          )}
        </div>
        {portal ? null : isCurrentUserAdmin() ? (
          <div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} style={{ display:'none' }} />
            <button disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}
              style={{ padding:'10px 18px', borderRadius:8, border:'none', background:busy?'#9bb59b':'#2d4a2d', color:'#fff', fontWeight:800, fontSize:14, cursor:busy?'default':'pointer', fontFamily:'inherit' }}>
              {busy ? 'Reading…' : '📸 Import basis sheet'}
            </button>
          </div>
        ) : (
          <div style={{ fontSize:12, color:'#9bb59b', textAlign:'right' }}>📸 Import is admin-only<div style={{ fontSize:10 }}>{getCurrentUserEmail() ? `signed in as ${getCurrentUserEmail()}` : 'not signed in'}</div></div>
        )}
      </div>

      {err && <div style={{ background:'#fdecea', border:'1px solid #e0a0a0', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'#c0392b' }}>{err} <button onClick={()=>setErr(null)} style={{ marginLeft:8, background:'none', border:'none', cursor:'pointer', color:'#c0392b', fontWeight:800 }}>✕</button></div>}

      {/* Review grid (post-extract, pre-save) */}
      {review && (
        <div style={{ ...card, border:'2px solid #2d4a2d' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:10 }}>
            <div style={{ fontWeight:800, color:'#1a2e1a' }}>Review — {review.rows.length} cells{review.version ? ` · Version ${review.version}` : ''}{review.updated ? ` · ${review.updated}` : ''}</div>
            <div style={{ display:'flex', gap:8 }}>
              <button disabled={busy} onClick={saveReview} style={{ padding:'8px 16px', borderRadius:7, border:'none', background:'#2d6a1a', color:'#fff', fontWeight:800, cursor:'pointer', fontFamily:'inherit' }}>{busy?'Saving…':'✓ Save board'}</button>
              <button disabled={busy} onClick={()=>setReview(null)} style={{ padding:'8px 16px', borderRadius:7, border:'1px solid #c8d8c0', background:'#fff', color:'#4a6e4a', fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
            </div>
          </div>
          <div style={{ fontSize:11, color:'#60735f', marginBottom:10 }}>Fix any mis-read basis below; cash previews live off futures. Then Save.</div>
          {!!(review.vanished || []).length && (
            <div style={{ background:'#fdf5e6', border:'1px solid #e6d3a3', borderRadius:8,
                          padding:'10px 12px', marginBottom:12, fontSize:12 }}>
              <b>{review.vanished.length} cell{review.vanished.length===1?'':'s'} on the current board {review.vanished.length===1?'was':'were'} not read from this photo.</b>{' '}
              Saving replaces the board, so {review.vanished.length===1?'it':'they'} will be gone. If the
              elevator really withdrew {review.vanished.length===1?'that bid':'those bids'} that is correct —
              otherwise retake the photo, because a row missed here is a target that quietly loses its price.
              <div style={{ marginTop:6, color:'#6b5a2a', maxHeight:120, overflowY:'auto' }}>
                {review.vanished.slice(0,40).map((v,i) => (
                  <div key={i}>{v.commodity} · {v.location} · {v.ship_month} · {v.cme_contract}
                    {v.basis!=null ? ' · ' + (Number(v.basis)>=0?'+':'') + Math.round(Number(v.basis)*100) + 'c' : ''}</div>))}
                {review.vanished.length>40 && <div>…and {review.vanished.length-40} more</div>}
              </div>
            </div>)}
          {BOARD_COMMODITY_ORDER.filter(c => review.rows.some(r=>r.commodity===c)).map(commodity => {
            const { locs, dels, cell } = matrixFor(commodity, review.rows);
            return (
              <div key={commodity} style={{ marginBottom:16 }}>
                <div style={{ fontWeight:800, color:'#2d4a2d', marginBottom:4 }}>{boardCommodityLabel(commodity)}</div>
                <div style={{ overflowX:'auto' }}><table style={{ borderCollapse:'collapse' }}>
                  <thead><tr><th style={{ ...th, textAlign:'left', position:'sticky', left:0, zIndex:2, background:'#fff', borderRight:'1px solid #e3ece3' }}>Ship / CME</th>{locs.map(l => <th key={l} style={th}>{l}<div style={{ fontWeight:400, textTransform:'none' }}>{termForLocation(l)==='pickup'?'P/U':'dlvd'}</div></th>)}</tr></thead>
                  <tbody>{dels.map(d => (
                    <tr key={d.cme+d.ship}>
                      <td style={{ ...td, textAlign:'left', fontWeight:600, position:'sticky', left:0, zIndex:1, background:'#fff', borderRight:'1px solid #e3ece3' }}>{d.ship} <span style={{ color:'#9bb59b', fontSize:11 }}>{d.cme}</span></td>
                      {locs.map(l => { const r = cell[(d.cme+'|'+d.ship)+'@@'+l]; if(!r) return <td key={l} style={{ ...td, color:'#ccc' }}>—</td>;
                        const c = cashOf(commodity, d.cme, r.basis);
                        return <td key={l} style={td}>
                          <input value={r.basis} onChange={e => { const v = e.target.value; setReview(prev => ({ ...prev, rows: prev.rows.map(x => x===r ? { ...x, basis: v==='' || v==='-' ? v : Number(v) } : x) })); }}
                            style={{ width:56, textAlign:'right', border:'1px solid #dde7dd', borderRadius:4, padding:'2px 4px', fontSize:12, fontFamily:'inherit' }} />
                          <div style={{ fontSize:10, color:'#2d6a1a', marginTop:1 }}>{cashFmt(c)}</div>
                        </td>; })}
                    </tr>
                  ))}</tbody>
                </table></div>
              </div>
            );
          })}
        </div>
      )}

      {/* Controls */}
      {!review && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
          {portal && commodsPresent.length > 1 && <>
            <span style={{ fontSize:12, color:'#60735f', fontWeight:700 }}>Crop:</span>
            {commodsPresent.map(c => <button key={c} onClick={()=>{ setPortalCrop(c); setPortalLoc(''); }} style={btn(effCrop===c)}>{boardCommodityLabel(c)}</button>)}
            <span style={{ width:12 }} />
          </>}
          <span style={{ fontSize:12, color:'#60735f', fontWeight:700 }}>Show:</span>
          <button onClick={()=>setViewMode('cash')} style={btn(viewMode==='cash')}>Cash</button>
          <button onClick={()=>setViewMode('basis')} style={btn(viewMode==='basis')}>Basis</button>
          <span style={{ width:12 }} />
          {!portal && <button onClick={()=>setTermFilter('all')} style={btn(termFilter==='all')}>All</button>}
          <button onClick={()=>setTermFilter('delivered')} style={btn(termFilter==='delivered')}>Delivered</button>
          <button onClick={()=>setTermFilter('pickup')} style={btn(termFilter==='pickup')}>Picked up</button>
          {portal && portalLocs.length > 0 && (
            <>
              <span style={{ fontSize:12, color:'#60735f', fontWeight:700, marginLeft:4 }}>Location:</span>
              <select value={effLoc} onChange={e=>setPortalLoc(e.target.value)}
                style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #c8d8c0', fontSize:12, fontFamily:'inherit', color:'#1a2e1a', background:'#fff' }}>
                {portalLocs.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </>
          )}
        </div>
      )}

      {/* Dashboard */}
      {!review && loading && <div style={{ color:'#60735f', padding:24 }}>Loading board…</div>}
      {!review && !loading && !commodsPresent.length && (
        <div style={card}><strong style={{ color:'#2d4a2d' }}>No basis sheet loaded yet.</strong>
          <div style={{ fontSize:13, color:'#60735f', marginTop:6 }}>Tap 📸 Import basis sheet, photograph your printed sheet, review, and save — cash then stays live off futures on its own.</div></div>
      )}
      {/* Portal: one location at a time — a simple bid list, not the full sheet */}
      {!review && !loading && portal && (() => {
        if (!effCrop) return null;
        const mine = rows.filter(r => r.commodity===effCrop && r.location===effLoc).sort((a,b)=>(a.ship_sort||0)-(b.ship_sort||0));
        if (!effLoc || !mine.length) return <div style={card}><span style={{ color:'#60735f', fontSize:13 }}>No {boardCommodityLabel(effCrop).toLowerCase()} {termFilter==='pickup'?'picked-up':'delivered'} bids posted yet.</span></div>;
        return (
          <div style={card}>
            <div style={{ fontWeight:800, color:'#1a2e1a', fontSize:15 }}>{boardCommodityLabel(effCrop)}</div>
            <div style={{ fontSize:11, color:'#60735f', marginBottom:8 }}>{effLoc} · {termFilter==='pickup'?'picked up':'delivered'}</div>
            <table style={{ borderCollapse:'collapse', width:'100%', maxWidth:420 }}>
              <thead><tr>
                <th style={{ ...th, textAlign:'left' }}>Ship / CME</th>
                <th style={th}>{viewMode==='cash'?'Cash':'Basis'}</th>
              </tr></thead>
              <tbody>{mine.map(r => {
                const c = cashOf(effCrop, r.cme_contract, r.basis);
                return (
                  <tr key={r.cme_contract+r.ship_month}>
                    <td style={{ ...td, textAlign:'left', fontWeight:600 }}>{r.ship_month} <span style={{ color:'#9bb59b', fontSize:11 }}>{r.cme_contract}</span></td>
                    <td style={td}>{viewMode==='cash'
                      ? <><span style={{ fontWeight:700, color:'#1a2e1a' }}>{cashFmt(c)}</span><div style={{ fontSize:10, color:'#60735f' }}>{basisFmt(r.basis)}</div></>
                      : <span style={{ fontWeight:700, color: r.basis<0?'#c0392b':'#2d6a1a' }}>{basisFmt(r.basis)}</span>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        );
      })()}
      {/* Admin: the full all-locations comparison sheet */}
      {!review && !loading && !portal && commodsPresent.map(commodity => {
        const { locs, dels, cell } = matrixFor(commodity, rows);
        if (!locs.length || !dels.length) return null;
        return (
          <div key={commodity} style={card}>
            <div style={{ fontWeight:800, color:'#1a2e1a', fontSize:15, marginBottom:8 }}>{boardCommodityLabel(commodity)}</div>
            <div style={{ overflowX:'auto' }}><table style={{ borderCollapse:'collapse', width:'100%' }}>
              <thead><tr><th style={{ ...th, textAlign:'left', position:'sticky', left:0, zIndex:2, background:'#fff', borderRight:'1px solid #e3ece3' }}>Ship / CME</th>{locs.map(l => <th key={l} style={th}>{l}<div style={{ fontWeight:400, textTransform:'none', color:'#9bb59b' }}>{termForLocation(l)==='pickup'?'picked up':'delivered'}</div></th>)}</tr></thead>
              <tbody>{dels.map(d => (
                <tr key={d.cme+d.ship}>
                  <td style={{ ...td, textAlign:'left', fontWeight:600, position:'sticky', left:0, zIndex:1, background:'#fff', borderRight:'1px solid #e3ece3' }}>{d.ship} <span style={{ color:'#9bb59b', fontSize:11 }}>{d.cme}</span>
                    {/* Futures strength belongs to the ROW: every location in it
                        is priced off this one contract, while basis differs per
                        location. Rendered only where the archive spans enough
                        contract years — corn holds 12, soybeans and wheat hold 2
                        and correctly decline. */}
                    {(() => { const fg = futStrengthByContract[`${commodity==='plenish'?'soybeans':commodity}|${d.cme}`];
                      return fg ? <div title={`${fg.label} — settle ${Number(fg.settle).toFixed(4)} as of ${fg.asOf}`} style={{ fontSize:10, fontWeight:700, color:STRENGTH_COLOR[fg.category], marginTop:1 }}>
                        fut {ordinal(fg.fillPct)}
                      </div> : null; })()}
                  </td>
                  {locs.map(l => { const r = cell[(d.cme+'|'+d.ship)+'@@'+l]; if(!r) return <td key={l} style={{ ...td, color:'#ccc' }}>—</td>;
                    const c = cashOf(commodity, d.cme, r.basis);
                    const g = strengthByCell[`${commodity}@@${d.cme}|${d.ship}@@${l}`];
                    return <td key={l} style={td}>
                      {viewMode==='cash'
                        ? <><span style={{ fontWeight:700, color:'#1a2e1a' }}>{cashFmt(c)}</span><div style={{ fontSize:10, color:'#60735f' }}>{basisFmt(r.basis)}</div></>
                        : <span style={{ fontWeight:700, color: r.basis<0?'#c0392b':'#2d6a1a' }}>{basisFmt(r.basis)}</span>}
                      {/* Only where the archive can defensibly answer. A cell
                          with no chip means "not enough comparable history",
                          never "average" — so nothing is rendered rather than a
                          neutral-looking 50th. */}
                      {g && <div title={`${g.label} — ${g.detail}`} style={{ marginTop:2, fontSize:10, fontWeight:800, letterSpacing:.2, color:STRENGTH_COLOR[g.category] }}>
                        {ordinal(g.fillPct)}
                      </div>}
                    </td>; })}
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        );
      })}
    </div>
  );
}
// ── Live Bids (Kevin only, sql/118) ──────────────────────────────────────────
// Kevin, 2026-10-30: "I have to look in app, then look at basis sheet, then do the
// math to get a bid. 3 steps." This is the one step: every location and delivery
// month on Howlett's posted sheet (grain_basis_board, kept current by the Cash Board
// photo import) priced off LIVE CQG futures that his office PC posts about once a
// minute (api/_live-futures.js). For him only ("Just for me"): the tab is offered
// only to a login in live_bid_viewers, and RLS refuses the quotes to anyone else.
//
// WHERE THE FUTURES CAME FROM IS ALWAYS ON SCREEN. A bid is only as good as the
// futures under it, so each contract says whether it is live, stale (the PC stopped
// posting while the market is open) or yesterday's settle, and a bid off anything
// but a live price is drawn muted with a marker. A stale bid that looks live is the
// failure this page exists to prevent.
const LIVE_BID_BLOCKS = [
  { key: 'corn', label: 'Corn' },
  { key: 'soybeans', label: 'Soybeans' },
  { key: 'plenish', label: 'Plenish' },
  { key: 'wheat', label: 'Wheat' },
];
const liveBidPrice = (n) => {
  if (n == null || isNaN(n)) return '—';
  const s = Number(n).toFixed(4).replace(/0{1,2}$/, '');
  return `$${s}`;
};
const liveBidAge = (ms) => {
  if (ms == null) return '';
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} hr ago` : `${Math.round(h / 24)} days ago`;
};
// a move in dollars as cents, quarters as fractions: 0.0025 -> "¼¢", -0.0125 -> "1¼¢"
const liveBidCents = (d) => {
  const c = Math.abs(d) * 100;
  const whole = Math.floor(c + 1e-9), frac = Math.round((c - whole) * 4) / 4;
  const f = frac === 0.25 ? '¼' : frac === 0.5 ? '½' : frac === 0.75 ? '¾' : '';
  const w = frac === 1 ? whole + 1 : whole;
  return `${w || !f ? w : ''}${f}¢`;
};
// Up/down colours: a deep green and red that read on white at small sizes (both
// better than 5:1), and pale tints of the same for the tick flash, which fades back
// through the CSS transition.
const LIVE_UP = '#1d7a3a', LIVE_DOWN = '#b42318', LIVE_FLAT = '#1a2e1a';
const liveDirColor = (d) => d > 0 ? LIVE_UP : d < 0 ? LIVE_DOWN : LIVE_FLAT;
const flashBg = (dir, rest = '#fff') => dir === 'up' ? 'rgba(29,122,58,0.16)' : dir === 'down' ? 'rgba(180,35,24,0.14)' : rest;
// the day's move as "▲2¾¢" / "▼¼¢" / "unch"
const liveDayMove = (d) => d == null ? '' : d === 0 ? 'unch' : `${d > 0 ? '▲' : '▼'}${liveBidCents(d)}`;
const liveBidClock = (iso) => iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '';

// ── Live Bids charts ("mini chart ... behind a click drill in", then "add more charts",
// Kevin, Oct 30). Tapping a futures box opens one contract's chart: Today from the
// minute line the office PC feeds (live_futures_ticks, sql/119), and 1M / 3M / All from
// the USDA settles Silas already keeps. Points are spaced by index, not clock time, so
// nights, weekends and the morning break don't leave flat gaps.
const LIVE_CHART_RANGES = [['today', 'Today'], ['1m', '1M'], ['3m', '3M'], ['all', 'All']];
const liveChartTime = (d) => d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
const liveChartDay = (d) => d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
// the current session: walk back from the newest minute until a gap of 3+ hours (the
// afternoon close to the evening open, or a weekend)
function liveSessionCut(rowsAsc) {
  let i = rowsAsc.length - 1;
  while (i > 0 && rowsAsc[i].t - rowsAsc[i - 1].t < 3 * 3600 * 1000) i--;
  return rowsAsc.slice(i);
}

function LiveSparkChart({ pts, baseline, baselineLabel, fmt, height = 168 }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  if (!pts || pts.length < 2) return null;
  const W = 340, H = height, L = 6, R = 54, T = 26, B = 22;
  const vals = pts.map(p => p.v).concat(baseline != null ? [baseline] : []);
  const max = Math.max(...vals), min = Math.min(...vals), span = (max - min) || 0.01;
  const x = i => L + (i / (pts.length - 1)) * (W - L - R);
  const y = v => T + (1 - (v - min) / span) * (H - T - B);
  const ref = baseline != null ? baseline : pts[0].v;
  const col = liveDirColor(pts[pts.length - 1].v - ref);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join('');
  const hi = hover != null ? pts[hover] : null;
  const pick = (e) => {
    const r = svgRef.current && svgRef.current.getBoundingClientRect();
    if (!r || !r.width) return;
    const sx = ((e.clientX - r.left) / r.width) * W;
    setHover(Math.max(0, Math.min(pts.length - 1, Math.round(((sx - L) / (W - L - R)) * (pts.length - 1)))));
  };
  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Price chart"
      style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'pan-y', userSelect: 'none' }}
      onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHover(null)}>
      {baseline != null && <>
        <line x1={L} x2={W - R} y1={y(baseline)} y2={y(baseline)} stroke="#9aa79c" strokeDasharray="3 4" strokeWidth="1" />
        <text x={W - R + 5} y={y(baseline) + 3.5} fontSize="10" fill="#7d8a7d">{baselineLabel}</text>
      </>}
      <path d={`${d}L${x(pts.length - 1).toFixed(1)},${H - B}L${L},${H - B}Z`} fill={col} opacity="0.08" />
      <path d={d} fill="none" stroke={col} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(pts.length - 1)} cy={y(pts[pts.length - 1].v)} r="3.5" fill={col} />
      <text x={W - R + 5} y={T + 4} fontSize="10" fill="#7d8a7d">{liveBidPrice(max)}</text>
      <text x={W - R + 5} y={H - B} fontSize="10" fill="#7d8a7d">{liveBidPrice(min)}</text>
      <text x={L} y={H - 5} fontSize="10" fill="#7d8a7d">{fmt(pts[0])}</text>
      <text x={W - R} y={H - 5} fontSize="10" fill="#7d8a7d" textAnchor="end">{fmt(pts[pts.length - 1])}</text>
      {hi && <>
        <line x1={x(hover)} x2={x(hover)} y1={T - 4} y2={H - B} stroke="#1a2e1a" strokeWidth="1" opacity="0.35" />
        <circle cx={x(hover)} cy={y(hi.v)} r="4" fill="#fff" stroke={col} strokeWidth="2" />
        <text x={Math.min(Math.max(x(hover), L + 50), W - R - 50)} y={12} fontSize="11.5" fontWeight="700" fill="#1a2e1a" textAnchor="middle">
          {liveBidPrice(hi.v)} · {fmt(hi)}
        </text>
      </>}
    </svg>
  );
}

function LiveFuturesChart({ f, commodity, cme, quote, isMobile, onClose }) {
  const [range, setRange] = useState('today');
  const [ticks, setTicks] = useState(null);     // [{ t, v }] for the current session
  const [daily, setDaily] = useState(null);     // [{ t, v, date }] settles, oldest first
  const [err, setErr] = useState(null);
  const sym = f.sym;
  const cm = cmeToContractMonth(cme);
  const key = GRAIN_CROP_TO_COMMODITY[BOARD_COMMODITY_TO_CROP[commodity]];
  useEffect(() => {
    let cancelled = false;
    const loadTicks = () => sym && sbFetch('live_futures_ticks', { select: 'minute,last', filters: [`symbol=eq.${sym}`], order: 'minute.desc', limit: 1000, fresh: true })
      .then(r => { if (!cancelled) setTicks(liveSessionCut((r || []).map(x => ({ t: new Date(x.minute).getTime(), v: Number(x.last) })).reverse())); })
      .catch(e => { if (!cancelled) { setTicks([]); setErr(e.message); } });
    loadTicks();
    if (key && cm) sbFetch('futures_settles_daily', { select: 'settle_date,settle_price', filters: ['source=eq.usda_ams', `commodity=eq.${key}`, `contract_month=eq.${encodeURIComponent(cm)}`], order: 'settle_date.asc', limit: 1000 })
      .then(r => { if (!cancelled) setDaily((r || []).filter(x => x.settle_price != null).map(x => ({ t: new Date(`${x.settle_date}T00:00:00Z`).getTime(), v: Number(x.settle_price), date: x.settle_date }))); })
      .catch(() => { if (!cancelled) setDaily([]); });
    const id = setInterval(() => { if (!document.hidden) loadTicks(); }, 30000);
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('keydown', esc); };
  }, [sym, key, cm, onClose]);

  const live = f.source === 'live' || f.source === 'stale';
  let pts = null, baseline = null, baselineLabel = '', fmt = liveChartTime, note = null;
  if (range === 'today') {
    pts = (ticks || []).map(p => ({ ...p, label: liveChartTime(new Date(p.t)) }));
    if (live && pts.length && f.price != null) pts = [...pts, { t: Date.now(), v: f.price }];
    baseline = f.settle ?? null; baselineLabel = 'settle';
    fmt = p => liveChartTime(new Date(p.t));
    if (ticks && pts.length < 2) note = "Today's line fills in as prices come in from the office PC.";
  } else {
    const days = { '1m': 31, '3m': 92, all: 100000 }[range];
    const since = Date.now() - days * 86400000;
    pts = (daily || []).filter(p => p.t >= since);
    if (live && f.price != null && pts.length) pts = [...pts, { t: Date.now(), v: f.price, today: true }];
    fmt = p => p.today ? 'now' : liveChartDay(new Date(p.t));
    if (daily && pts.length < 2) note = 'Not enough settles stored for this contract yet.';
  }
  const loading = range === 'today' ? ticks == null : daily == null;
  const first = pts && pts.length ? pts[0].v : null, last = pts && pts.length ? pts[pts.length - 1].v : null;
  const rangeMove = range !== 'today' && first != null && last != null ? Math.round((last - first) * 10000) / 10000 : null;
  const hiV = pts && pts.length ? Math.max(...pts.map(p => p.v)) : null, loV = pts && pts.length ? Math.min(...pts.map(p => p.v)) : null;
  const dayHigh = range === 'today' && quote?.high != null ? Math.max(Number(quote.high), hiV ?? -Infinity) : hiV;
  const dayLow = range === 'today' && quote?.low != null ? Math.min(Number(quote.low), loV ?? Infinity) : loV;
  const crop = { corn: 'Corn', soybeans: 'Soybeans', plenish: 'Soybeans', wheat: 'Wheat' }[commodity] || '';
  const seg = (on) => ({ flex: 1, height: 34, border: 'none', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, fontWeight: on ? 800 : 600, background: on ? '#fff' : 'transparent', color: on ? '#1a2e1a' : '#60735f', boxShadow: on ? '0 1px 2px rgba(26,46,26,.18)' : 'none', cursor: 'pointer' });
  const stat = (label, v) => <div><div style={{ fontSize: 10.5, color: '#7d8a7d', textTransform: 'uppercase', letterSpacing: .3 }}>{label}</div><div style={{ fontSize: 14, fontWeight: 700, color: '#1a2e1a', fontVariantNumeric: 'tabular-nums' }}>{v}</div></div>;

  // Rendered into <body>: inside the app's scroll area the sheet sat UNDER the
  // phone's bottom tab bar (Kevin, 2026-10-30: "I can't expand the chart, when I
  // try it scrolls the page behind the chart"). On a phone it is a full-screen
  // page that scrolls on its own and never hands the swipe to the page behind.
  const chartH = isMobile ? 250 : 168;
  const body = (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: isMobile ? '#fff' : 'rgba(16,30,18,.38)', display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', fontFamily: T.sans }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`${cm} ${crop} chart`}
        style={isMobile
          ? { width: '100%', height: '100%', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', background: '#fff', padding: 'calc(14px + env(safe-area-inset-top, 0px)) 16px calc(24px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box' }
          : { width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 -8px 30px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, color: '#60735f', fontWeight: 700 }}>{cm} {crop}{f.source === 'settle' ? ' · last settle' : f.source === 'stale' ? ' · stale' : ''}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#1a2e1a', fontVariantNumeric: 'tabular-nums' }}>
              {liveBidPrice(f.price)}{' '}
              {f.source === 'live' && f.dayChg != null && <span style={{ fontSize: 16, color: liveDirColor(f.dayChg) }}>{liveDayMove(f.dayChg)}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ height: 36, padding: '0 14px', borderRadius: 18, border: '1px solid #dde7dd', background: '#fff', fontFamily: 'inherit', fontWeight: 700, color: '#1a2e1a', cursor: 'pointer' }}>Done</button>
        </div>
        <div style={{ display: 'flex', gap: 2, background: '#eef2ec', borderRadius: 10, padding: 3, margin: '10px 0 6px' }}>
          {LIVE_CHART_RANGES.map(([k, l]) => <button key={k} style={seg(range === k)} onClick={() => setRange(k)}>{l}</button>)}
        </div>
        <div style={{ minHeight: chartH, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: isMobile ? '8px 0' : 0 }}>
          {loading ? <span style={{ color: '#7d8a7d', fontSize: 13 }}>Loading chart…</span>
            : note ? <span style={{ color: '#7d8a7d', fontSize: 13, textAlign: 'center', padding: '0 20px' }}>{note}</span>
            : <LiveSparkChart key={range} pts={pts} baseline={baseline} baselineLabel={baselineLabel} fmt={fmt} height={chartH} />}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 6 }}>
          {range === 'today'
            ? <>{stat('Settle', liveBidPrice(f.settle))}{stat('High', liveBidPrice(dayHigh))}{stat('Low', liveBidPrice(dayLow))}</>
            : <>{stat(`${LIVE_CHART_RANGES.find(r => r[0] === range)[1]} move`, rangeMove == null ? '—' : <span style={{ color: liveDirColor(rangeMove) }}>{liveDayMove(rangeMove)}</span>)}{stat('High', liveBidPrice(hiV))}{stat('Low', liveBidPrice(loV))}</>}
        </div>
        <div style={{ fontSize: 11, color: '#8a978a', marginTop: 8 }}>
          {range === 'today' ? 'Office PC prices by the minute; touch the line to read a price.' : `USDA settles${daily && daily.length ? ` since ${liveChartDay(new Date(daily[0].t))}` : ''}; the last point is the live price.`}
          {err && ` Could not read today's line: ${err}`}
        </div>
      </div>
    </div>
  );
  return typeof document !== 'undefined' && document.body ? createPortal(body, document.body) : body;
}

// Home shortcut to Live Bids. Kevin: "This will be for no one else except us
// two" (him and Mike). Same gate as the Grain Marketing tab: an admin login whose
// live_bid_viewers self-read (sql/118) returns its own row. A failed or empty
// read renders nothing. Mounted on the AGENT Home branch only, never the portal.
function LiveBidsHomeButton({ isMobile, onOpen }) {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    if (!isCurrentUserAdmin()) return;
    let live = true;
    sbFetch('live_bid_viewers', { select: 'email', fresh: true })
      .then(r => { if (live) setOk(!!(r && r.length)); })
      .catch(() => { if (live) setOk(false); });
    return () => { live = false; };
  }, []);
  if (!ok) return null;
  return (
    <button onClick={onOpen}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
               padding: isMobile ? '14px 16px' : '14px 20px', border: 'none', borderRadius: 12,
               background: '#2d4a2d', color: '#fff', cursor: 'pointer', textAlign: 'left',
               boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
      <span style={{ fontSize: 22, lineHeight: 1 }}>⚡</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: isMobile ? 16 : 17, fontWeight: 800 }}>Live Cash Bids</span>
        <span style={{ display: 'block', fontSize: 12.5, opacity: 0.8, marginTop: 2 }}>Corn, soybeans and wheat off live futures</span>
      </span>
      <span style={{ fontSize: 20, opacity: 0.85 }}>›</span>
    </button>
  );
}

function LiveBidBoard({ liveFutures, isMobile }) {
  const [board, setBoard] = useState(null);       // grain_basis_board rows
  const [extra, setExtra] = useState([]);         // bid_board_extra_basis rows (Kreher's)
  const [noBids, setNoBids] = useState([]);       // the sheet's "no bid" cells, from the archive
  const [quotes, setQuotes] = useState([]);       // live_futures_quotes
  const [quoteErr, setQuoteErr] = useState(null);
  const [err, setErr] = useState(null);
  // the crop and location you last looked at, kept on this device only
  const saved = useMemo(() => { try { return JSON.parse(localStorage.getItem('silas.liveBids.view') || '{}') || {}; } catch { return {}; } }, []);
  const [crop, setCrop] = useState(saved.crop || 'corn');
  const [loc, setLoc] = useState(saved.loc || '');  // '' = every location
  useEffect(() => { try { localStorage.setItem('silas.liveBids.view', JSON.stringify({ crop, loc })); } catch { /* private mode */ } }, [crop, loc]);
  const [chartCme, setChartCme] = useState(null);   // the futures box whose chart is open
  const closeChart = useCallback(() => setChartCme(null), []);
  const [now, setNow] = useState(Date.now());
  const [keyOut, setKeyOut] = useState(null);     // { key_id, secret } shown once
  const [keyBusy, setKeyBusy] = useState(false);
  const [showPc, setShowPc] = useState(false);
  // Price movement on screen ("Can we add a flash graphic ... Add colors for which way?",
  // Kevin, Oct 30): a contract that moved since the last poll flashes green or red,
  // cleared after the fade. The first load sets the baseline and flashes nothing.
  const prevPx = useRef(null);
  const [flash, setFlash] = useState({});         // symbol -> 'up' | 'down'

  const loadSheet = useCallback(() => {
    Promise.all([
      sbFetch('grain_basis_board', { select: 'commodity,location,cme_contract,ship_month,ship_sort,basis,sheet_version,sheet_updated', order: 'commodity.asc,ship_sort.asc', fresh: true }),
      sbFetch('bid_board_extra_basis', { select: 'program,commodity,location,cme_contract,ship_month,ship_sort,basis,no_bid,sheet_version,sheet_updated', order: 'ship_sort.asc', fresh: true }).catch(() => []),
    ]).then(([b, x]) => {
      setBoard(b || []); setExtra(x || []);
      const ver = (b || [])[0]?.sheet_version;
      // grain_basis_board holds priced cells only; the import keeps the sheet's
      // refusals in the archive. Without them a "no bid" would read as a blank.
      if (ver) {
        sbFetch('basis_sheet_observations', { select: 'commodity,source,source_label,futures_contract,delivery_window', filters: ['no_bid=is.true', `sheet_version=eq.${encodeURIComponent(ver)}`], fresh: true })
          .then(r => setNoBids(r || [])).catch(() => setNoBids([]));
      }
    }).catch(e => setErr(e.message));
  }, []);
  const loadQuotes = useCallback(() => {
    // limit keeps this to ONE request: without it sbFetch speculates five extra
    // pages in parallel, and at a 10-second poll that is six round trips for 16 rows.
    // The table holds one row per contract (at most a few dozen).
    sbFetch('live_futures_quotes', { select: '*', limit: 200, fresh: true })
      .then(r => {
        const rows = r || [];
        const px = Object.fromEntries(rows.map(q => [q.symbol, Number(q.last)]));
        const before = prevPx.current;
        prevPx.current = px;
        if (before) {
          const moved = {};
          for (const [sym, p] of Object.entries(px)) {
            const was = before[sym];
            if (was == null || !isFinite(p) || !isFinite(was)) continue;
            const d = Math.round((p - was) * 10000) / 10000;
            if (d !== 0) moved[sym] = d > 0 ? 'up' : 'down';
          }
          if (Object.keys(moved).length) setFlash(moved);
        }
        setQuotes(rows); setQuoteErr(null);
      })
      .catch(e => setQuoteErr(e.message));
  }, []);
  useEffect(() => { loadSheet(); loadQuotes(); }, [loadSheet, loadQuotes]);
  useEffect(() => {
    if (!Object.keys(flash).length) return undefined;
    const t = setTimeout(() => setFlash({}), 1600);
    return () => clearTimeout(t);
  }, [flash]);
  useEffect(() => {
    // every 10 s while the page is open; the office PC sends every 15 s ("is it possible
    // to have the numbers move as we are on the page?", Kevin, Oct 30)
    const q = setInterval(() => { if (!document.hidden) loadQuotes(); }, 10000);
    const t = setInterval(() => setNow(Date.now()), 5000);
    const vis = () => { if (!document.hidden) { loadQuotes(); setNow(Date.now()); } };
    document.addEventListener('visibilitychange', vis);
    return () => { clearInterval(q); clearInterval(t); document.removeEventListener('visibilitychange', vis); };
  }, [loadQuotes]);

  // every priced or refused cell, in one shape, whichever table it came from
  const cells = useMemo(() => {
    const out = [];
    for (const r of board || []) out.push({ block: r.commodity, commodity: r.commodity, location: r.location, cme: r.cme_contract, ship: r.ship_month, sort: r.ship_sort || 0, basis: Number(r.basis), noBid: false });
    for (const r of noBids) {
      const block = /^plenish_/i.test(r.source) ? 'plenish' : r.commodity;
      out.push({ block, commodity: block, location: r.source_label, cme: r.futures_contract, ship: r.delivery_window, sort: null, basis: null, noBid: true });
    }
    // Kreher's is a corn LOCATION, not a crop (Kevin, Oct 30). Its windows (Oct-Nov,
    // Jan-Feb, Mar) don't line up with the sheet's months, so it keeps its own little
    // table under Corn. A window the sheet marks "no bid" is one Kreher's isn't buying:
    // Clarence takes corn Oct-Nov only, Wayne County year round.
    for (const r of extra) {
      if (r.no_bid || r.basis == null) continue;
      out.push({ block: r.commodity, program: r.program, commodity: r.commodity, location: `${r.program === 'krehers' ? "Kreher's " : ''}${r.location}`, cme: r.cme_contract, ship: r.ship_month, sort: r.ship_sort || 0, basis: Number(r.basis), noBid: false });
    }
    return out;
  }, [board, noBids, extra]);

  const quoteBySym = useMemo(() => Object.fromEntries((quotes || []).map(q => [q.symbol, q])), [quotes]);
  // Futures for one contract: the live quote if there is one, else the last USDA settle.
  const futuresFor = (commodity, cme) => {
    const sym = liveFutures_symbolFor(commodity, cme);
    const q = sym && quoteBySym[sym];
    const cm = cmeToContractMonth(cme);
    const key = GRAIN_CROP_TO_COMMODITY[BOARD_COMMODITY_TO_CROP[commodity]];
    const hit = (liveFutures?.commodities?.[key] || []).find(r => r.contract_month === cm);
    if (q) {
      const f = liveFutures_freshness(q.received_at, new Date(now));
      const price = Number(q.last);
      // The day's move is figured against the last USDA settle Silas holds, never the
      // sheet's own "Close": CQG's Close column tracks the live price through the
      // session, so price - Close reads near zero all day (found Oct 30, Dec corn
      // showed ~0 while it was 2¾¢ under the Oct 29 settle).
      const settle = hit && hit.settle_price != null ? Number(hit.settle_price) : null;
      const dayChg = settle != null && isFinite(price) ? Math.round((price - settle) * 10000) / 10000 : null;
      return { sym, price, dayChg, settle, source: f.state === 'stale' ? 'stale' : 'live', state: f.state, ageMs: f.ageMs, at: q.received_at };
    }
    if (hit && hit.settle_price != null) return { sym, price: Number(hit.settle_price), change: null, source: 'settle', asOf: liveFutures?.grain_as_of };
    return { sym, price: null, source: 'none' };
  };

  // the feed as a whole: the newest quote among the contracts the sheet uses
  const wanted = useMemo(() => [...new Set(cells.map(c => liveFutures_symbolFor(c.commodity, c.cme)).filter(Boolean))], [cells]);
  const newest = wanted.map(s => quoteBySym[s]?.received_at).filter(Boolean).sort().pop() || null;
  const feed = liveFutures_freshness(newest, new Date(now));
  const missing = wanted.filter(s => !quoteBySym[s]);

  const blocksPresent = LIVE_BID_BLOCKS.filter(b => cells.some(c => c.block === b.key));
  const effCrop = blocksPresent.some(b => b.key === crop) ? crop : (blocksPresent[0]?.key || 'corn');
  const inBlock = cells.filter(c => c.block === effCrop);
  const mainLocs = [...new Set(inBlock.filter(c => !c.program).map(c => c.location))];
  const progLocs = [...new Set(inBlock.filter(c => c.program).map(c => c.location))];
  const locs = [...mainLocs, ...progLocs];
  const effLoc = locs.includes(loc) ? loc : '';
  // the main table is the sheet's own locations; a program (Kreher's) gets its own table,
  // and one picked location shows only its own ship months
  const mine = effLoc ? inBlock.filter(c => c.location === effLoc) : inBlock.filter(c => !c.program);
  const progCells = effLoc ? [] : inBlock.filter(c => c.program);
  const progRows = [];
  for (const c of progCells) if (!progRows.some(r => r.k === `${c.cme}|${c.ship}`)) progRows.push({ k: `${c.cme}|${c.ship}`, cme: c.cme, ship: c.ship, sort: c.sort, commodity: c.commodity });
  progRows.sort((a, b) => a.sort - b.sort);
  // delivery rows in sheet order; a "no bid" row takes the order of the priced row beside it
  const rowMap = new Map();
  for (const c of mine) {
    const k = `${c.cme}|${c.ship}`;
    const cur = rowMap.get(k);
    if (!cur) rowMap.set(k, { k, cme: c.cme, ship: c.ship, sort: c.sort, commodity: c.commodity });
    else if (cur.sort == null && c.sort != null) cur.sort = c.sort;
  }
  // A row the sheet refused at EVERY location (wheat Jun 27 on 10-B) has no priced
  // cell to take its order from; it sits just ahead of the first row on its contract.
  for (const r of rowMap.values()) {
    if (r.sort != null) continue;
    const same = [...rowMap.values()].filter(x => x.cme === r.cme && x.sort != null).map(x => x.sort);
    r.sort = same.length ? Math.min(...same) - 0.5 : 999;
  }
  const rows = [...rowMap.values()].sort((a, b) => a.sort - b.sort);
  const cellAt = {}; for (const c of [...mine, ...progCells]) cellAt[`${c.cme}|${c.ship}@@${c.location}`] = c;
  const contracts = [...new Set([...rows, ...progRows].map(r => r.cme))];

  async function makeKey() {
    if (!window.confirm('Create a new key for the office PC? The old key stops working right away.')) return;
    setKeyBusy(true); setKeyOut(null);
    try {
      const res = await adminApiFetch('/api/market?fn=live-futures-key', { method: 'POST' });
      const body = await apiJson(res, 'the key service');
      setKeyOut({ key_id: body.key_id, secret: body.secret });
    } catch (e) { setKeyOut({ error: e.message }); }
    setKeyBusy(false);
  }

  const card = { background: '#fff', border: '1px solid #dde7dd', borderRadius: 10, padding: isMobile ? 12 : 16, marginBottom: 14 };
  const chip = (on) => ({ padding: '7px 12px', borderRadius: 18, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: '1px solid', background: on ? '#2d4a2d' : '#fff', color: on ? '#fff' : '#2d4a2d', borderColor: on ? '#2d4a2d' : '#c8d8c0', fontFamily: 'inherit' });
  const BANNER = {
    live:   { bg: '#eaf5e4', bd: '#b9dca8', fg: '#1f5a12', text: `Live futures · updated ${liveBidAge(feed.ageMs)}` },
    stale:  { bg: '#fdecea', bd: '#e8a9a0', fg: '#a12b1c', text: `Futures are stale: last update ${liveBidAge(feed.ageMs)}. Check the office PC is on with CQG and the Excel sheet open. Do not quote off these bids.` },
    closed: { bg: '#f2f4f2', bd: '#d5ddd5', fg: '#4a5a4a', text: `Market closed · last live price ${liveBidClock(newest)}` },
    none:   { bg: '#fdf3e2', bd: '#e8c98a', fg: '#7a5410', text: `No live futures yet. Bids below use the last settle${liveFutures?.grain_as_of ? ` (${liveFutures.grain_as_of})` : ''} until the office PC is connected.` },
  }[feed.state];

  // one bid cell: big bid, basis beneath; muted with a marker unless the futures are live
  const bidCell = (row, location, big) => {
    const c = cellAt[`${row.cme}|${row.ship}@@${location}`];
    if (!c) return <span style={{ color: '#b9c9b9' }}>—</span>;
    if (c.noBid) return <span style={{ color: '#c0392b', fontSize: 12, fontWeight: 600 }}>no bid</span>;
    const f = futuresFor(row.commodity, row.cme);
    const b = liveFutures_bid(f.price, c.basis);
    // Basis is fixed for the day, so a cash bid moves exactly with its futures month:
    // the bid takes the futures' day colour, and the day's move sits beside the basis.
    const day = f.source === 'live' ? f.dayChg : null;
    const color = f.source === 'live' ? liveDirColor(day || 0) : f.source === 'stale' ? '#a12b1c' : '#7d8a7d';
    const mark = f.source === 'live' ? '' : f.source === 'stale' ? ' !' : f.source === 'settle' ? ' s' : '';
    return (
      <span style={{ display: 'inline-block', textAlign: 'right', borderRadius: 6, padding: '2px 6px', margin: '-2px -6px', background: flashBg(f.sym && flash[f.sym], 'transparent'), transition: 'background-color 1.2s ease-out' }}>
        <span style={{ fontSize: big ? 20 : 15, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', transition: 'color .4s' }}>{liveBidPrice(b)}<sup style={{ fontSize: 10 }}>{mark}</sup></span>
        <br /><span style={{ fontSize: 11, color: '#60735f', fontVariantNumeric: 'tabular-nums' }}>
          {basisFmt(c.basis)}
          {day != null && <span style={{ color: liveDirColor(day), fontWeight: 700, marginLeft: 5 }}>{liveDayMove(day)}</span>}
        </span>
      </span>
    );
  };

  if (err) return <div style={{ ...card, color: '#c0392b' }}>Could not load the basis sheet: {err}</div>;
  if (!board) return <div style={{ ...card, color: '#60735f' }}>Loading bids…</div>;
  const sheetVer = board[0]?.sheet_version, sheetUpd = board[0]?.sheet_updated;
  const th = { padding: '6px 8px', textAlign: 'right', fontSize: 10.5, color: '#60735f', textTransform: 'uppercase', letterSpacing: .3, borderBottom: '2px solid #e3ece3', whiteSpace: 'nowrap', verticalAlign: 'bottom' };
  const td = { padding: '7px 8px', textAlign: 'right', borderBottom: '1px solid #f0f4f0', whiteSpace: 'nowrap', verticalAlign: 'top' };
  const pcCard = (
    <div style={card}>
      <button onClick={() => setShowPc(v => !v)} style={{ background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 800, color: '#1a2e1a', cursor: 'pointer' }}>
        {showPc ? '▾' : '▸'} Office PC connection
      </button>
      {showPc && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#1a2e1a', lineHeight: 1.5 }}>
          <div>Last price received: {newest ? `${liveBidClock(newest)} (${liveBidAge(now - new Date(newest))})` : 'never'}.</div>
          <div style={{ marginTop: 8 }}>
            The office PC signs what it sends with a key. Create one here and paste it into the helper on that PC when it asks. Making a new key switches the old one off.
          </div>
          <button disabled={keyBusy} onClick={makeKey} style={{ ...chip(true), marginTop: 10, borderRadius: 8 }}>{keyBusy ? 'Creating…' : 'Create PC key'}</button>
          {keyOut?.error && <div style={{ color: '#c0392b', marginTop: 8 }}>{keyOut.error}</div>}
          {keyOut?.secret && (
            <div style={{ marginTop: 10, padding: 10, background: '#f2f6f2', border: '1px solid #cfe4c4', borderRadius: 8 }}>
              <div style={{ fontWeight: 700 }}>Shown once. Paste both into the helper now.</div>
              <div style={{ marginTop: 6 }}>Key number: <code style={{ fontSize: 14 }}>{keyOut.key_id}</code></div>
              <div style={{ marginTop: 4, wordBreak: 'break-all' }}>Secret: <code style={{ fontSize: 13 }}>{keyOut.secret}</code></div>
              <button style={{ ...chip(false), marginTop: 8, borderRadius: 8 }} onClick={() => navigator.clipboard && navigator.clipboard.writeText(`${keyOut.key_id} ${keyOut.secret}`)}>Copy both</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
  const commodityOf = (cme) => ([...rows, ...progRows].find(r => r.cme === cme) || {}).commodity || effCrop;
  const chartEl = chartCme && (() => {
    const cmdty = commodityOf(chartCme);
    const f = futuresFor(cmdty, chartCme);
    return <LiveFuturesChart f={f} commodity={cmdty} cme={chartCme} quote={f.sym ? quoteBySym[f.sym] : null} isMobile={isMobile} onClose={closeChart} />;
  })();

  // ── Phone layout (Kevin, Oct 30: "Definitely need to optimize for mobile, thats what
  // will be used the most"; "Phone mockup looks good, push that live"). The crop switch
  // and the futures strip stay pinned while the bids scroll; each futures box opens its
  // chart; locations sit on one sideways row; one location reads as big bids by ship
  // month, and All reads as a card per ship month listing every location.
  if (isMobile) {
    const G = '#f2f6f2', LINE = '#e1e9df', INK = '#1a2e1a', MUTED = '#60735f';
    const seg = (on) => ({ flex: 1, minWidth: 0, height: 40, border: 'none', borderRadius: 9, fontFamily: 'inherit', fontSize: 14, fontWeight: on ? 800 : 600, background: on ? '#fff' : 'transparent', color: on ? INK : MUTED, boxShadow: on ? '0 1px 2px rgba(26,46,26,.18)' : 'none', cursor: 'pointer', padding: 0 });
    const pill = (on) => ({ flex: '0 0 auto', height: 40, padding: '0 16px', borderRadius: 20, border: `1px solid ${on ? '#2d4a2d' : '#c8d8c0'}`, background: on ? '#2d4a2d' : '#fff', color: on ? '#fff' : '#2d4a2d', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' });
    const scroller = { display: 'flex', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', margin: '0 -14px', padding: '0 14px' };
    const bidFor = (row, location, size) => {
      const c = cellAt[`${row.cme}|${row.ship}@@${location}`];
      if (!c) return <span style={{ color: '#b9c9b9' }}>—</span>;
      if (c.noBid) return <span style={{ color: '#c0392b', fontSize: 13, fontWeight: 600 }}>no bid</span>;
      const f = futuresFor(row.commodity, row.cme);
      const b = liveFutures_bid(f.price, c.basis);
      const day = f.source === 'live' ? f.dayChg : null;
      const color = f.source === 'live' ? liveDirColor(day || 0) : f.source === 'stale' ? '#a12b1c' : '#7d8a7d';
      const mark = f.source === 'live' ? '' : f.source === 'stale' ? ' !' : f.source === 'settle' ? ' s' : '';
      return (
        <span style={{ display: 'inline-block', textAlign: 'right', borderRadius: 8, padding: '2px 6px', margin: '-2px -6px', background: flashBg(f.sym && flash[f.sym], 'transparent'), transition: 'background-color 1.2s ease-out' }}>
          <span style={{ fontSize: size, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{liveBidPrice(b)}<sup style={{ fontSize: 10 }}>{mark}</sup></span>
          <span style={{ display: 'block', fontSize: 12, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
            {basisFmt(c.basis)}{day != null && <span style={{ color: liveDirColor(day), fontWeight: 700 }}> · {liveDayMove(day)}</span>}
          </span>
        </span>
      );
    };
    const monthCard = (r, locList, title) => (
      <div key={`${title || ''}${r.k}`} style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px 6px' }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: INK }}>{r.ship}{title ? <span style={{ fontSize: 12, fontWeight: 600, color: MUTED }}> · {title}</span> : null}</span>
          <span style={{ fontSize: 12, color: MUTED }}>off {cmeToContractMonth(r.cme) || r.cme}</span>
        </div>
        {locList.filter(l => cellAt[`${r.cme}|${r.ship}@@${l}`]).map(l => (
          <button key={l} onClick={() => setLoc(l)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, minHeight: 52, padding: '8px 14px', border: 'none', borderTop: `1px solid ${LINE}`, background: 'transparent', fontFamily: 'inherit', textAlign: 'left', color: INK, cursor: 'pointer' }}>
            <span style={{ fontSize: 14, fontWeight: 600, minWidth: 0 }}>{l}</span>
            {bidFor(r, l, 18)}
          </button>
        ))}
      </div>
    );
    return (
      <div>
        {feed.state !== 'live' && (
          <div style={{ ...card, background: BANNER.bg, borderColor: BANNER.bd, color: BANNER.fg, fontSize: 13, fontWeight: 700 }}>{BANNER.text}</div>
        )}
        {/* pinned while the bids scroll */}
        <div style={{ position: 'sticky', top: 0, zIndex: 5, background: G, margin: '0 -14px', padding: '8px 14px', borderBottom: `1px solid ${LINE}` }}>
          <div style={{ display: 'flex', gap: 2, background: '#e3eae0', borderRadius: 11, padding: 3 }}>
            {blocksPresent.map(b => <button key={b.key} style={seg(effCrop === b.key)} onClick={() => { setCrop(b.key); setLoc(''); }}>{b.label}</button>)}
          </div>
          <div style={{ ...scroller, marginTop: 10, paddingBottom: 2 }}>
            {contracts.map(cme => {
              const f = futuresFor(commodityOf(cme), cme);
              const day = f.source === 'live' ? f.dayChg : null;
              return (
                <button key={cme} onClick={() => setChartCme(cme)} aria-label={`${cmeToContractMonth(cme)} chart`}
                  style={{ flex: '0 0 auto', minWidth: 90, textAlign: 'left', padding: '7px 10px', borderRadius: 10, border: `1px solid ${f.source === 'live' ? '#cfe3c4' : LINE}`, background: flashBg(flash[f.sym]), transition: 'background-color 1.2s ease-out', fontFamily: 'inherit', cursor: 'pointer' }}>
                  <div style={{ fontSize: 11, color: MUTED, fontWeight: 700, letterSpacing: '.02em' }}>{cmeToContractMonth(cme) || cme}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: f.source === 'live' ? INK : '#7d8a7d', fontVariantNumeric: 'tabular-nums' }}>{liveBidPrice(f.price)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: day != null ? liveDirColor(day) : '#8a978a', fontVariantNumeric: 'tabular-nums' }}>
                    {day != null ? liveDayMove(day) : f.source === 'stale' ? 'stale' : f.source === 'settle' ? 'settle' : '—'}
                  </div>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
            <span style={{ color: BANNER.fg }}>●</span> {feed.state === 'live' ? `Live · updated ${liveBidAge(feed.ageMs)}` : feed.state === 'closed' ? 'Market closed' : feed.state === 'stale' ? 'Futures stale' : 'Last settle'} · tap a month for its chart
          </div>
        </div>

        <div style={{ ...scroller, padding: '10px 14px' }}>
          <button style={pill(effLoc === '')} onClick={() => setLoc('')}>All</button>
          {locs.map(l => <button key={l} style={pill(effLoc === l)} onClick={() => setLoc(l)}>{l}</button>)}
        </div>

        <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
          {effLoc ? (
            <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, overflow: 'hidden' }}>
              {rows.map((r, i) => (
                <div key={r.k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, minHeight: 58, padding: '10px 14px', borderTop: i ? `1px solid ${LINE}` : 'none' }}>
                  <div><div style={{ fontSize: 16, fontWeight: 800, color: INK }}>{r.ship}</div><div style={{ fontSize: 12, color: MUTED }}>off {cmeToContractMonth(r.cme) || r.cme}</div></div>
                  {bidFor(r, effLoc, 22)}
                </div>
              ))}
            </div>
          ) : (
            <>
              {rows.map(r => monthCard(r, mainLocs))}
              {progRows.map(r => monthCard(r, progLocs, "Kreher's"))}
            </>
          )}
        </div>

        <div style={{ fontSize: 11.5, color: MUTED, marginBottom: 14, lineHeight: 1.5 }}>
          Bid = futures + basis from sheet {sheetVer ? `Version ${sheetVer}` : '(no version)'}{sheetUpd ? `, updated ${sheetUpd}` : ''}. Green and red show the day's move against yesterday's settle. <b>s</b> = last settle, <b>!</b> = stale.
          {quoteErr && <div>Could not read live futures: {quoteErr}</div>}
          {feed.state !== 'none' && missing.length > 0 && <div>No live price yet for {missing.map(s => liveFutures_parseSymbol(s)?.label || s).join(', ')} (using the last settle).</div>}
        </div>
        {pcCard}
        {chartEl}
      </div>
    );
  }


  return (
    <div>
      <div style={{ ...card, background: BANNER.bg, borderColor: BANNER.bd, color: BANNER.fg, fontSize: 14, fontWeight: 700 }}>
        {BANNER.text}
        {quoteErr && <div style={{ fontWeight: 500, fontSize: 12, marginTop: 4 }}>Could not read live futures: {quoteErr}</div>}
        {feed.state !== 'none' && missing.length > 0 && (
          <div style={{ fontWeight: 500, fontSize: 12, marginTop: 4 }}>No live price yet for {missing.map(s => liveFutures_parseSymbol(s)?.label || s).join(', ')} (using the last settle).</div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {blocksPresent.map(b => <button key={b.key} style={chip(effCrop === b.key)} onClick={() => { setCrop(b.key); setLoc(''); }}>{b.label}</button>)}
      </div>

      {/* the futures under this crop's bids, one chip per contract; two to a row on a
          phone, where one-per-line chips pushed the bids off the screen */}
      <div style={isMobile ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 } : { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {contracts.map(cme => {
          const f = futuresFor(commodityOf(cme), cme);
          const tone = f.source === 'live' ? '#1f5a12' : f.source === 'stale' ? '#a12b1c' : '#7d8a7d';
          const day = f.source === 'live' ? f.dayChg : null;
          return (
            <button key={cme} onClick={() => setChartCme(cme)} title="Open the chart" style={{ fontFamily: 'inherit', cursor: 'pointer', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: isMobile ? 'space-between' : 'flex-start', columnGap: isMobile ? 4 : 6, border: `1px solid ${f.source === 'live' ? '#b9dca8' : '#dde7dd'}`, borderRadius: 8, padding: isMobile ? '6px 8px' : '5px 10px', background: flashBg(flash[f.sym]), transition: 'background-color 1.2s ease-out', fontSize: 12, whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 700, color: '#1a2e1a' }}>{cmeToContractMonth(cme) || cme}</span>
              <span style={{ fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums' }}>{liveBidPrice(f.price)}</span>
              {day != null && <span title={`Change from the ${liveBidPrice(f.settle)} settle`} style={{ fontWeight: 700, color: liveDirColor(day), fontVariantNumeric: 'tabular-nums' }}>{liveDayMove(day)}</span>}
              {/* a fresh price's age is already in the banner; say it here only when it's off */}
              {f.source !== 'live' && <span style={{ color: '#8a978a', fontSize: 11 }}>{f.source === 'stale' ? `stale ${liveBidAge(f.ageMs)}` : f.source === 'settle' ? 'settle' : 'no price'}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        <button style={chip(effLoc === '')} onClick={() => setLoc('')}>All locations</button>
        {locs.map(l => <button key={l} style={chip(effLoc === l)} onClick={() => setLoc(l)}>{l}</button>)}
      </div>

      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {effLoc ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Ship</th>
              <th style={th}>Futures</th>
              <th style={th}>{effLoc}</th>
            </tr></thead>
            <tbody>{rows.map(r => {
              const f = futuresFor(r.commodity, r.cme);
              return (
                <tr key={r.k}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: '#1a2e1a' }}>{r.ship}</td>
                  <td style={{ ...td, fontSize: 12, color: '#60735f' }}>{cmeToContractMonth(r.cme) || r.cme}<br /><span style={{ fontVariantNumeric: 'tabular-nums' }}>{liveBidPrice(f.price)}</span></td>
                  <td style={td}>{bidCell(r, effLoc, true)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: '#fff' }}>Ship</th>
              {mainLocs.map(l => <th key={l} style={{ ...th, whiteSpace: 'normal', minWidth: 70 }}>{l}</th>)}
            </tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.k}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: '#1a2e1a', position: 'sticky', left: 0, background: '#fff' }}>
                  {r.ship}<br /><span style={{ fontSize: 11, fontWeight: 500, color: '#60735f' }}>{cmeToContractMonth(r.cme) || r.cme}</span>
                </td>
                {mainLocs.map(l => <td key={l} style={td}>{bidCell(r, l, false)}</td>)}
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {progRows.length > 0 && (
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: '#fff' }}>Ship</th>
              {progLocs.map(l => <th key={l} style={{ ...th, whiteSpace: 'normal', minWidth: 70 }}>{l}</th>)}
            </tr></thead>
            <tbody>{progRows.map(r => (
              <tr key={r.k}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: '#1a2e1a', position: 'sticky', left: 0, background: '#fff' }}>
                  {r.ship}<br /><span style={{ fontSize: 11, fontWeight: 500, color: '#60735f' }}>{cmeToContractMonth(r.cme) || r.cme}</span>
                </td>
                {progLocs.map(l => <td key={l} style={td}>{bidCell(r, l, false)}</td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 11.5, color: '#60735f', marginBottom: 14, lineHeight: 1.5 }}>
        Bid = futures + basis. Basis from sheet {sheetVer ? `Version ${sheetVer}` : '(no version)'}{sheetUpd ? `, updated ${sheetUpd}` : ''}; to change it, photograph the new sheet on the Cash Board tab.
        {' '}Green and red show the day's move against yesterday's settle; a price flashes when it ticks. Click a futures box for its chart. A bid marked <b>s</b> uses the last settle, and <b>!</b> means the live price has gone stale.
      </div>

      {pcCard}
      {chartEl}
    </div>
  );
}

function GrainMarketingView({ farmers = [], isMobile = false, mode = null, initialSelection = null, onConsumeInitialSelection = null }) {
  const isPortal = mode === "portal";   // farmers can edit their own contracts/plan, but NOT agency-wide actions (Cash Board basis import)
  const [selectedFarmerId, setSelectedFarmerId] = useState(null);
  // The book's own crop year, not a typed 2026, so the page rolls with the book
  // (Kevin, 2026-11-01: Silas must move "seamlessly ... from one crop year to the next").
  const [cropYear, setCropYear]                 = useState(() => CURRENT_CROP_YEAR);
  const [tab, setTab]                           = useState('dashboard');
  const [contracts, setContracts]               = useState([]);
  const [planRows, setPlanRows]                 = useState([]);
  const [costRows, setCostRows]                 = useState([]);
  const [fwdCurveRows, setFwdCurveRows]         = useState([]);
  const [fwdSave, setFwdSave]                   = useState(null);   // {status:'saving'|'ok'|'err', at} — forward-curve save feedback
  const [futuresRef, setFuturesRef]             = useState([]);
  // Live daily CME/CBOT settles parsed from USDA's own reports (api/_futures.js).
  // Feeds the Forward Price Curve FUTURES column + the whole tool's mark price.
  const [liveFutures, setLiveFutures]           = useState(null); // { commodities, grain_as_of, ratio }
  const [copRows, setCopRows]                   = useState([]);   // grain_cop rows
  const [copSave, setCopSave]                   = useState(null); // {status:'saving'|'ok'|'err', at} — COP save feedback
  const [boardRows, setBoardRows]               = useState([]);   // grain_basis_board (Howlett's posted basis by location/term)
  const [fwdVal, setFwdVal]                     = useState({});   // per-crop { term:'pickup'|'delivered', location } for valuing unpriced bu off the board
  const [topTab, setTopTab]                     = useState('cashboard'); // 'cashboard' | 'tool' | 'markets' — Cash Board is the landing
  // ── The Cash Board admin switch (app_config.cash_board_enabled, sql/087) ──
  // Howlett asked that its posted cash bids not be shown to customers, so the
  // board is PAUSED rather than deleted. null = not read yet. Only an admin
  // (Kevin / Mike) can see the board while it is off, and only an admin can flip
  // it — RLS on app_config refuses a write from anybody else.
  // isCurrentUserAdmin() is the EFFECTIVE identity, so Preview-as shows exactly
  // what the farmer gets. It FAILS CLOSED for a non-admin: a read that errors
  // leaves the board hidden, because the reason for the switch is privacy.
  const cbAdmin = isCurrentUserAdmin();
  const [cashBoardOn, setCashBoardOn]           = useState(null);
  const [cashBoardSaving, setCashBoardSaving]   = useState(false);
  useEffect(() => {
    let live = true;
    sbFetch('app_config', { select: 'cash_board_enabled', filters: ['id=eq.1'], fresh: true })
      .then(r => { if (live) setCashBoardOn(r && r[0] ? r[0].cash_board_enabled !== false : false); })
      .catch(() => { if (live) setCashBoardOn(false); });
    return () => { live = false; };
  }, []);
  const toggleCashBoard = useCallback(async (next) => {
    setCashBoardSaving(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/app_config?id=eq.1`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json', Prefer: 'return=representation' },
        body: JSON.stringify({ cash_board_enabled: next, updated_at: new Date().toISOString(), updated_by: getCurrentUserEmail() || null }),
      });
      if (!res.ok) throw new Error(await res.text());
      const out = await res.json();
      // RLS filters a refused UPDATE to ZERO rows with a 200 — an empty
      // representation means the switch did NOT move, whatever the status says.
      if (!out || !out.length) throw new Error('not saved — only an admin can change this');
      setCashBoardOn(out[0].cash_board_enabled !== false);
    } catch (err) { setError('Cash Board switch: ' + (err.message || err)); }
    setCashBoardSaving(false);
  }, []);
  const cashBoardVisible = cbAdmin || cashBoardOn === true;
  // A switched-OFF board is still a tab for an admin (so it can be turned back
  // on), but it is not where an admin LANDS: opening Grain onto a red 'Turn on'
  // banner reads as a broken page. It shows once somebody clicks the tab.
  const [cbPicked, setCbPicked]                 = useState(false);
  // Live Bids (sql/118): offered only to a login in live_bid_viewers — Kevin, who
  // said the board is "Just for me". The self-read policy returns his own row or
  // nothing, so a failed read leaves the tab hidden. Never in the portal.
  const [liveBidsOk, setLiveBidsOk]             = useState(false);
  useEffect(() => {
    if (isPortal || !cbAdmin) return;
    let live = true;
    sbFetch('live_bid_viewers', { select: 'email', fresh: true })
      .then(r => { if (live) setLiveBidsOk(!!(r && r.length)); })
      .catch(() => { if (live) setLiveBidsOk(false); });
    return () => { live = false; };
  }, [isPortal, cbAdmin]);
  const effTopTab = (topTab === 'cashboard' && (!cashBoardVisible || (cashBoardOn === false && !cbPicked))) ? 'tool'
    : (topTab === 'livebids' && !liveBidsOk) ? 'tool' : topTab;
  const [planCrop, setPlanCrop]                 = useState('Corn');      // crop shown in the Marketing Plan detail (lifted so the positions overview can drill in)
  const [copCrop, setCopCrop]                   = useState('Corn'); // COP tool crop selector
  const [loading, setLoading]                   = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [error, setError]                       = useState(null);
  const [modal, setModal]                       = useState(null);
  const [editingContract, setEditingContract]   = useState(null);
  const [contractForm, setContractForm]         = useState({});
  const [cropFilter, setCropFilter]             = useState('All');
  const [hideFilled, setHideFilled]             = useState(false);
  // ONE opener for "new contract". The blank-form literal was pasted at three
  // call sites, so a new field (or a changed default) had to be remembered in
  // three places — and the Contracts-tab button below would have made a fourth.
  // `crop` lets a caller pre-select the crop the user is already looking at.
  const openNewContract = (crop = 'Corn') => {
    setEditingContract(null);
    setContractForm({
      crop, contract_type: 'Fixed Price',
      futures_price: getFutures(crop).price,
      futures_month_name: 'Dec', futures_month_year: cropYear,
      basis: -0.25, bushels_contracted: '', buyer: '',
      delivery_start: '', delivery_end: '', notes: '', contract_number: '',
    });
    setModal('contract');
  };
  const [sortCol, setSortCol]                   = useState(null);   // Contracts table sort column (label) — null = file order
  const [sortDir, setSortDir]                   = useState('asc');
  const toggleSort = (col) => {
    if (sortCol === col) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); }
    else { setSortCol(col); setSortDir('asc'); }
  };
  // Value a contract sorts by for a given column label. Returns a number or a
  // string; null/'' are treated as empty and always sorted last (either direction).
  const contractSortVal = (c, col) => {
    const dnum = v => (v == null || v === '' ? null : Number(v));
    switch (col) {
      case 'Contract #':  return (c.contract_number || '') + '|' + (c.pricing_line_id || '');
      case 'Crop':        return c.crop || '';
      case 'Buyer':       return c.buyer || '';
      case 'Delivery':    return c.location || '';
      case 'Bushels':     return cBushels(c);
      case 'Delivered':   return contractDelivery[c.id] || 0;
      case 'Type':        return cType(c);
      case 'Futures':     return dnum(cFutures(c));
      case 'Basis':       return dnum(c.basis);
      case 'Net Price':   return dnum(netPrice(c));
      case 'Written':     return c.contract_date || '';
      case 'Ship Month':  return c.ship_month || '';
      case 'Futures Mo.': return c.futures_month || c.fmonth || '';
      case 'Del. Start':  return c.delivery_start || c.dstart || '';
      case 'Del. End':    return c.delivery_end || c.dend || '';
      case 'Notes':       return c.notes || '';
      default:            return '';
    }
  };
  const contractCmp = (a, b) => {
    const av = contractSortVal(a, sortCol), bv = contractSortVal(b, sortCol);
    const aE = av == null || av === '' || (typeof av === 'number' && isNaN(av));
    const bE = bv == null || bv === '' || (typeof bv === 'number' && isNaN(bv));
    if (aE && bE) return 0;
    if (aE) return 1;          // empties always last
    if (bE) return -1;
    const r = (typeof av === 'number' && typeof bv === 'number')
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortDir === 'asc' ? r : -r;
  };
  const [importPreview, setImportPreview]       = useState(null);
  // Pre-import delta vs the farmer's existing csv_import rows. Built at
  // preview time so the user sees "X update · Y insert · Z remove" before
  // confirming, and consumed at confirm time so each pricing line maps to
  // PATCH (existing), INSERT (new), or DELETE (stale — present in DB but
  // not in the upload). The Bushel export is treated as the source of
  // truth FOR ITS OWN ELEVATOR: a csv_import row that isn't in the incoming
  // file is removed only when it carries the same buyer (grainImportStaleIds).
  // Manual modal entries (source != 'csv_import') are never touched.
  // Shape: { existingByKey, protectedKeys, existingRows, updateCount,
  //          protectedCount, insertCount }. Stale ids are DERIVED from
  //          existingRows + the buyer, which is still editable in the panel.
  const [importDelta, setImportDelta]           = useState(null);
  // Who bought the grain in the file being imported. The export carries no buyer
  // column — one file is one elevator — so it is asked for once per upload and
  // written to every row, rather than pulled out of the `account` column, which
  // is the SELLER. Defaulted from what the file itself names (grainBuyerFromRows)
  // or from the buyer already on this farm's imported contracts; blank blocks the
  // import, because an unlabelled contract is what let 103961/104502 be entered
  // twice. { value, from } — `from` is shown so a default never passes as a fact.
  const [importBuyer, setImportBuyer]           = useState({ value: '', from: null });
  const [yearCounts, setYearCounts]             = useState({});  // { 2024: 50, 2025: 121, ... }

  const farmerList = useMemo(() => [...farmers].sort((a, b) => a.name.localeCompare(b.name)), [farmers]);
  // The main loader splits a farmer into ONE card per policy_number (key =
  // name+policy#), and a farmer can also span multiple DB farmer_ids. Grain
  // contracts/plan/cop are keyed by the DB farmer id (shared across a farmer's
  // policies), so they load fine — but crop-insurance data (approvedYields /
  // ppEligibility / scoEco) lives on each per-policy card. So a farmer with
  // corn+soy on one policy and wheat on another would only surface ONE policy's
  // crops here (find() returns the first card). Merge every same-name card so the
  // Crop Plan pulls insurance for ALL of the farmer's crops. (Silsby: corn/soy on
  // NY-944-6083921, wheat on NY-944-6087142 — was showing wheat only.)
  // Memoized so it's a STABLE object across renders — otherwise every render rebuilds
  // it, which cascades into `positions`/`farmMarketing` recomputing and effects (incl.
  // the plan auto-select) firing on every render, churning state mid-fetch.
  const selectedFarmer = useMemo(() => {
    const card = farmerList.find(f => f.id === selectedFarmerId);
    if (!card) return null;
    const nameKey = (card.name || '').trim().toLowerCase();
    const cards = farmerList.filter(f => (f.name || '').trim().toLowerCase() === nameKey);
    if (cards.length === 1) return card;
    const dedupe = (rows, keyFn) => {
      const seen = new Set();
      return rows.filter(r => { const k = keyFn(r); if (seen.has(k)) return false; seen.add(k); return true; });
    };
    const covKey = a => `${(a.county || '').toUpperCase()}|${(a.crop || '').toUpperCase()}|${(a.type || '').toUpperCase()}|${(a.practice || '').toUpperCase()}|${(a.plan || '').toUpperCase()}`;
    return {
      ...card,
      approvedYields:     dedupe(cards.flatMap(f => f.approvedYields || []), covKey),
      ppEligibility:      dedupe(cards.flatMap(f => f.ppEligibility || []), covKey),
      scoEcoEndorsements: cards.flatMap(f => f.scoEcoEndorsements || []),
      allFarmerDbIds:     [...new Set(cards.flatMap(f => f.allFarmerDbIds || f._allFarmerIds || [f.id]).filter(Boolean))],
    };
  }, [farmerList, selectedFarmerId]);

  // ── Summary tab (ROADMAP.md §1.2f / SILAS_ACTION_INTELLIGENCE.md Milestone
  // 2) — Silas Grain Action folded in as this tool's default landing view,
  // not a separate nav item. Admin/shadow only (Phase IV): the hook is only
  // ever given a real farmer when isCurrentUserAdmin() && !isPortal, so the
  // underlying fetch never fires for a portal/non-admin session — the fetch
  // itself is gated, not only what gets rendered from it.
  const [actionCrop, setActionCrop] = useState('Corn');
  // Grain Book View row -> Summary deep-link (ROADMAP.md §1.2f Milestone 4's
  // "click through" requirement — Silsby Corn 2026 must land exactly here).
  // Consumed ONCE: onConsumeInitialSelection clears the parent's state so
  // navigating away and back to Grain Marketing doesn't re-trigger a stale
  // selection, same precedent as the fieldsFarmerId/openFields deep-link.
  useEffect(() => {
    if (!initialSelection?.farmerId) return;
    setSelectedFarmerId(initialSelection.farmerId);
    if (initialSelection.cropYear != null) setCropYear(initialSelection.cropYear);
    if (initialSelection.crop) setActionCrop(initialSelection.crop);
    setTopTab('tool');
    if (onConsumeInitialSelection) onConsumeInitialSelection();
  }, [initialSelection]); // eslint-disable-line
  // Home's Live Cash Bids button opens straight onto the board. The tab itself
  // still only renders once liveBidsOk confirms the login, so this cannot show
  // the board to anyone the Grain tab would not.
  useEffect(() => {
    if (initialSelection?.topTab !== 'livebids') return;
    setTopTab('livebids');
    if (onConsumeInitialSelection) onConsumeInitialSelection();
  }, [initialSelection]); // eslint-disable-line
  const grainActionFarmer = (isCurrentUserAdmin() && !isPortal) ? selectedFarmer : null;
  const { result: actionResult, loading: actionLoading, error: actionError, onStorageSaved: onGrainStorageSaved } =
    useGrainActionResult({ farmer: grainActionFarmer, cropYear, crop: actionCrop, contracts, copRows });
  // Land on Summary the moment an admin picks a farm, every time a NEW farm is
  // picked — SILAS_ACTION_INTELLIGENCE.md §5: "the default farm landing page
  // should be the Summary / Action View." Non-admins/portal keep whatever tab
  // they're already on; 'dashboard' stays their effective default because the
  // Summary tab button never renders for them (see the tab bar below).

  // ONE ownership scope for every grain read on this page. A merged farmer card
  // spans several `farmers` rows; reading grain off the primary id alone made the
  // Marketing Tool disagree with the year badges and the positions overview,
  // which have always used the full list. Memoized on the joined string so the
  // array identity is stable across renders (an unstable dep here re-fires every
  // grain fetch — see the unstable-dep refetch loop that shipped once already).
  const grainOwnerIds = useMemo(() => {
    const ids = selectedFarmer?.allFarmerDbIds?.length ? selectedFarmer.allFarmerDbIds : (selectedFarmerId ? [selectedFarmerId] : []);
    return [...new Set(ids.filter(Boolean))];
  }, [selectedFarmer, selectedFarmerId]);
  const grainOwnerKey = grainOwnerIds.join(',');

  useEffect(() => {
    if (!selectedFarmerId) return;
    setLoading(true);
    setError(null);
    grainLoadAll(grainOwnerIds, cropYear)
      .then(({ contracts: c, plan: p, costs: co, fwdCurve: fc, futuresRef: fr, cop: cp }) => {
        setContracts(c);
        setPlanRows(p);
        setCostRows(co);
        setFwdCurveRows(fc);
        setFuturesRef(fr.length ? fr : buildDefaultFuturesRef(cropYear));
        setCopRows(cp || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedFarmerId, cropYear]);

  // Live grain futures board (USDA-published settlements) — loaded once; the
  // Forward Price Curve + mark price default to these until the user overrides.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/market?fn=futures')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && d.commodities) setLiveFutures(d); })
      .catch(() => { /* board just stays on stored/default prices */ });
    return () => { cancelled = true; };
  }, []);

  // Howlett's own posted basis board (grain_basis_board) — powers the "value
  // unpriced bushels at a destination" picker on the Forward Price Curve.
  useEffect(() => {
    let cancelled = false;
    // Gated on the Cash Board switch, like every reader of Howlett's posted bids.
    howlettBidsVisibleP().then(ok => ok ? sbFetch('grain_basis_board', { order: 'commodity.asc,ship_sort.asc' }) : [])
      .then(r => { if (!cancelled) setBoardRows(r || []); })
      .catch(() => { /* no board loaded yet — picker just won't show options */ });
    return () => { cancelled = true; };
  }, []);

  // Daily-scraped local elevator boards (basis_calculated_daily), surfaced as extra
  // "Delivered" destinations on the Forward Price Curve so unpriced bushels can be
  // valued at an elevator's actual posted basis, which refreshes every day. Add a
  // row to LOCAL_ELEVATORS to offer another local elevator.
  const [localBoards, setLocalBoards] = useState({});   // keyed by source → latest-bid_date rows
  useEffect(() => {
    let cancelled = false;
    LOCAL_ELEVATORS.forEach(({ commodity, source }) =>
      fetch(`/api/market?fn=basis-history&commodity=${commodity}&source=${source}`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          const rows = (d && Array.isArray(d.series)) ? d.series : [];
          if (cancelled || !rows.length) return;
          const latest = rows.reduce((mx, r) => (r.bid_date > mx ? r.bid_date : mx), '');
          setLocalBoards(prev => ({ ...prev, [source]: rows.filter(r => r.bid_date === latest) }));
        })
        .catch(() => {}));
    return () => { cancelled = true; };
  }, []);

  // Live settle ($/bu) for a grain crop + delivery-month label (e.g. 'Dec 26'),
  // or null when the board has no matching contract yet.
  function liveSettle(crop, month) {
    const arr = liveFutures?.commodities?.[GRAIN_CROP_TO_COMMODITY[crop]];
    if (!arr) return null;
    const hit = arr.find(r => r.contract_month === month);
    return hit && hit.settle_price != null ? Number(hit.settle_price) : null;
  }

  // Load contract counts grouped by crop_year (for the year selector
  // badges — so the user can see at a glance which years have data).
  // Refreshed when farmer changes or after an import / recalc.
  useEffect(() => {
    if (!selectedFarmerId) return;
    let cancelled = false;
    async function load() {
      try {
        const idFilter = grainOwnerKey;
        const rows = await sbFetch('grain_contracts', {
          filters: [`farmer_id=in.(${idFilter})`],
          select: 'crop_year',
        });
        if (cancelled) return;
        const counts = {};
        for (const r of (rows || [])) {
          if (r.crop_year != null) counts[r.crop_year] = (counts[r.crop_year] || 0) + 1;
        }
        setYearCounts(counts);
      } catch { /* table may not exist */ }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedFarmerId, contracts.length]);

  function buildDefaultFuturesRef(yr) {
    return marketingCrops.map(crop => ({
      crop_year: yr, crop,
      price: GRAIN_DEFAULT_FUTURES[yr]?.[crop]?.price || 4.50,
      ref_month: GRAIN_DEFAULT_FUTURES[yr]?.[crop]?.month || 'Dec',
    }));
  }

  function getFutures(crop) {
    // the BOARD crop: ORGANIC CORN is quoted, hedged and delivered as corn
    const bc = grainBaseCrop(crop);
    const row = futuresRef.find(r => r.crop === bc);
    // Prefer the live new-crop settle (first delivery month for the crop year,
    // e.g. Corn 'Dec 26' / Soybeans 'Nov 26' / Wheat 'Jul 26') so the whole tool
    // marks against today's board. Fall back to stored ref, then hardcoded default.
    const ncMonth = (GRAIN_DELIVERY_MONTHS[cropYear]?.[bc] || [])[0];
    const live = ncMonth ? liveSettle(bc, ncMonth) : null;
    const price = live != null ? live
      : (row?.price || GRAIN_DEFAULT_FUTURES[cropYear]?.[bc]?.price || 4.50);
    return { price, month: row?.ref_month || (ncMonth ? ncMonth.split(' ')[0] : '') };
  }

  // Pull APH, coverage level, and (intended) acres for a grain crop straight
  // from the farmer's crop-insurance records. APH + coverage come from
  // approvedYields (crop_coverage); acres prefers reported coverage acres and
  // falls back to PP-eligibility max-eligible acres ("intended" before acres
  // are reported). Acres-weighted APH; representative (max) coverage level.
  // The farmer card's coverage rows, restated in the raw `crop_coverage` shape the
  // shared resolver reads. ONE mapping, so the grain tool and everything else that
  // reads crop_coverage agree by construction.
  const grainCovRows = useMemo(() => (selectedFarmer?.approvedYields || []).map(a => ({
    county: a.county, crop: a.crop, plan: a.plan, type: a.type || a.typeCode || null,
    aph_yield: a.approvedYield, coverage_level: a.coverageLevel,
    acres: a.acres, price_election: a.priceElection, liability: a.liability,
    // PP comes across, and so do the two codes that key a row to its units. Without
    // ppAcres this mapping handed the resolver INSURED acres and it counted
    // prevent-plant ground as ground that grows bushels; without the codes the
    // per-unit split could never be found and it would silently model instead.
    pp_acres: a.ppAcres ?? null,
    type_code: a.typeCode ?? null, practice_code: a.practiceCode ?? null,
    // THE PRACTICE AND THE CROP YEAR, because organic is its own marketing
    // position. `practice_code` alone is not enough: the hand-loaded books
    // (Agri Sompo, ProAg) carry a practice NAME and no code, and without the
    // label their organic acres would silently read as conventional. The year
    // is what keeps a farm that grew organic in 2025 and not in 2026 from
    // being offered a position it no longer has.
    practice: a.practice ?? null, crop_year: a.cropYear ?? null,
    // THE PRICE ELECTION HAS TO COME ACROSS. guaranteeBushels() divides liability
    // by price × election; a row that arrives without it falls back to 1 and the
    // guarantee reads short by (1 − pct) — 45% on CAT. This mapping is the grain
    // tool's ONLY view of coverage, so a field missing here is a field the
    // forward-sell ceiling is computed without. Marquart's CAT potatoes are the
    // worked example: 119,118 cwt stated, 65,533 derived without this.
    price_election_pct: a.priceElectionPct ?? a.price_election_pct ?? null,
  })), [selectedFarmer]);

  // ── WHICH CROPS THIS FARM MARKETS ─────────────────────────────────────────
  // The three base crops, plus an ORGANIC row for each one this farm actually
  // has insured organic ACRES in. Kevin, 2026-09-05, relaying a farmer's own
  // request: "treat organic crops as a different crop... this should only
  // appear if they have insured organic acres."
  //
  // So a farm with no organic coverage sees exactly the three crops it saw
  // before — the feature is invisible to the 270 farms it does not apply to,
  // and that is asserted rather than assumed.
  //
  // WHY IT MATTERS ON THE FARMS IT DOES REACH: RMA rates and PRICES organic
  // separately, so a blended position is a guarantee true of neither field.
  // Palladino and Carley's CY2026 Onondaga corn is 383.47 organic acres at an
  // APH of 87.4 beside 228.28 conventional acres at 149.4 — blended that is
  // 110.5 bu/ac, and a forward-sell ceiling built on it is wrong in a direction
  // nobody can see. Ten farm/crop positions in the book are in that shape.
  const marketingCrops = useMemo(
    () => [...GRAIN_CROPS, ...grainOrganicCropsPresent(grainCovRows, GRAIN_CROPS, cropYear)],
    [grainCovRows, cropYear]
  );
  // Does this farm carry ANY organic position? Used to decide whether the page
  // says anything about practice at all — a farm with none should see no new
  // words, not a note explaining a split that does not apply to it.
  const hasOrganic = marketingCrops.length > GRAIN_CROPS.length;
  // A dep an effect can compare BY VALUE. marketingCrops is a fresh array
  // whenever the coverage rows change identity, so listing it directly would
  // refetch on every render that touched coverage.
  const marketingCropsKey = marketingCrops.join('|');

  // ── A CONTRACT BELONGS TO A PRACTICE, NOT JUST A COMMODITY ───────────────
  // Kevin, 2026-09-05: "someones organic corn contracts show against their
  // organic acres, and their conventional corn contracts show against their
  // conventional acres/insurance guarantees."
  //
  // Every `cropContracts(crop)` in this view goes through
  // here, so the ORGANIC CORN card and the Corn card cannot disagree about
  // which contract is whose. Where the farm insures ONE practice for a
  // commodity the answer is derived from the coverage and nothing changes;
  // where it insures BOTH and the contract does not say, it counts as
  // CONVENTIONAL — and the page reports that it did, so a default is never
  // mistaken for a practice somebody chose.
  const cropContracts = (mc) =>
    contracts.filter((c) => contractMatchesMarketingCrop(c, mc, grainCovRows, cropYear));

  // The contracts sitting on the CONVENTIONAL card by default — this farm
  // insures both practices for that commodity and the contract does not say
  // which will fill it. They ARE counted and marketed as conventional; this
  // exists so the page can say so, and a default never passes as a statement.
  //
  // ⚠ A FUNCTION, NOT A CONST. Written as a const here it runs during the
  // component body and reaches cBushels, which is declared ~450 lines below —
  // "Cannot access before initialization", and the whole page renders as the
  // error boundary. Deferring it to render time removes the ordering question
  // rather than answering it, which is the only fix that stays correct when
  // somebody moves a declaration later.
  const defaultedGroups = () => GRAIN_CROPS.map((base) => ({
    base,
    rows: defaultedPracticeContracts(contracts, base, grainCovRows, cropYear),
  })).filter((x) => x.rows.length);

  // ⚠ A DIFFERENT LIST, BECAUSE IT IS A DIFFERENT FACT. A grandfathered
  // contract predates the practice column, IS counted as conventional, and is
  // merely reported. An unassigned one was written after the field existed and
  // still says nothing, so it counts on NEITHER card and somebody has to place
  // it. Folding them together would either hide the second or re-open the
  // first, and the first is what cost R L JEFFRES 477,037 bu.
  const unassignedGroups = () => GRAIN_CROPS.map((base) => ({
    base,
    rows: unassignedPracticeContracts(contracts, base, grainCovRows, cropYear),
  })).filter((x) => x.rows.length);

  // Acres / APH / coverage / guarantee for a grain crop, off the ONE liability-first
  // per-line resolver (`grainBushelsFromCoverage`). It used to blend the whole crop
  // — sum acres, weight the APH, then `Math.max()` the coverage level — which
  // over-guaranteed any farm insured at two different levels and silently dropped
  // acre-less lines. `incomplete` now comes back so the card can say so.
  function cropInsurance(grainCrop) {
    if (!selectedFarmer) return null;
    const target = String(grainCrop).toUpperCase();
    const isMatch = (c) => {
      const u = (c || '').toUpperCase();
      return target === 'WHEAT' ? (u === 'WHEAT' || u === 'WINTER WHEAT') : u === target;
    };
    const b = grainBushelsFromCoverage(grainCovRows, grainCrop, cropYear);
    let acres = b?.acres || 0;
    // No reported acres anywhere on the crop → fall back to PP max-eligible
    // (INTENDED acres, pre-planting). Documented fallback: corn/soy acres arrive
    // after planting, so this is the only acre figure available in the spring.
    const acresVia = acres > 0 ? 'reported' : 'intended';
    if (acres === 0) {
      acres = (selectedFarmer.ppEligibility || []).filter(p => isMatch(p.crop))
        .reduce((s, p) => s + (Number(p.maxEligAcres) || Number(p.plantedAcres) || 0), 0);
    }
    const aph = b?.aph || 0, coverage = b?.coverage || 0;
    if (!aph && !coverage && !acres) return null;
    // CHOPPED ACRES COME OFF HERE AND NOWHERE ELSE. This is the single entry point
    // the grain tool reads its acres / APH / coverage / guarantee through, and it
    // lives inside GrainMarketingView — so the haircut reaches every grain surface
    // and cannot leak into the farmer card, the PDF or any insurance figure, which
    // is exactly the scope Kevin asked for. See grainChopAdjust for why the policy
    // itself does not move.
    // THROUGH THE SHARED RULE, not a second copy of the lookup. This was a
    // hand-rolled `.find()` matching `r.crop === grainCrop` case-SENSITIVELY,
    // beside choppedAcresFor's case-insensitive match — two answers to one
    // question. It also had no idea what crop it was reading, so a wheat plan
    // row carrying a chopped figure would have haircut a wheat sell ceiling.
    const chopped = choppedAcresFor(planRows, grainCrop);
    const adj = grainChopAdjust({
      // Bushel figures straight from the resolver — NOT re-derived from the rounded
      // display APH above. When the guarantee came from PP intended acres there is
      // no per-line liability to sum, so it stays null and the caller models it.
      guaranteeBu: b?.guaranteeBu ?? null,
      expectedBu: b?.expectedBu ?? null,
      acres,
    }, chopped);
    return {
      aph, coverage, acres, acresVia,
      guaranteeBu: adj.guaranteeBu,
      expectedBu: adj.expectedBu,
      // Acres that can actually deliver bushels. `acres` above stays the INSURED
      // figure so the card still reconciles to an SOI; this is what the guarantee
      // was scaled by.
      grainAcres: adj.chop.grainAcres,
      chop: adj.chop,
      liability: b?.liability ?? null,
      incomplete: b?.incomplete || [],
    };
  }

  // Acres / APH / coverage for a grain crop. Auto-pulled from crop insurance, with
  // a manual plan entry as the override: acres → insured/intended acres, APH →
  // approved yield, coverage → coverage level. Live crop insurance drives APH +
  // coverage for anyone with keyed acres; a manual plan entry is only the fallback
  // for grain-only / un-keyed farms. `getPlan` and `yieldInfo` BOTH read this, so
  // the two can never derive a different APH for the same crop.
  function planBasis(crop) {
    const row = planRows.find(r => r.crop === crop);
    const ins = cropInsurance(crop) || {};
    const yieldPerAcre = Number(row?.yield_per_acre) || 0;
    const acres = row?.acres != null ? Number(row.acres) : (Number(ins.acres) || 0);
    const aph = Number(ins.aph) > 0 ? Number(ins.aph) : (row?.aph_yield != null ? Number(row.aph_yield) : (yieldPerAcre || 0));
    const coverage = Number(ins.coverage) > 0 ? Number(ins.coverage) : (row?.coverage_level != null ? Number(row.coverage_level) : 0);
    // `acres` stays the PLANTED figure — it is what the grower typed or what the
    // policy reports, it is what cost/acre is charged on, and it is what ties to an
    // SOI. `grainAcres` is the part that can deliver BUSHELS, so expected production
    // runs off it. Two different questions, kept as two numbers rather than one that
    // is quietly wrong for whichever it is not being used for.
    // Third copy of the same lookup, now gated by the same rule. The clamp to
    // `acres` stays here because this one is a PLAN-row read rather than the
    // ceiling adjustment, and grainChopAdjust does its own capping downstream.
    const chopped = cropChopsForSilage(crop)
      ? Math.max(0, Math.min(acres, Number(row?.chopped_acres) || 0))
      : 0;
    return { row, ins, acres, aph, coverage, chopped, grainAcres: Math.max(0, acres - chopped) };
  }

  // ── Expected yield: ONE resolver, and it says WHAT KIND of number it is ───────
  // Three bases, in precedence order:
  //   'actual' — harvested, from `actual_production ÷ acres`. Beats everything:
  //              once the crop is in the bin nothing else is a better number.
  //   'keyed'  — a pre-harvest estimate typed on the Crop Plan (`yield_per_acre`),
  //              for the grower who knows this year runs above his APH.
  //   'aph'    — the crop-insurance approved yield. THE DEFAULT, so a farm with
  //              keyed coverage carries a real expected yield with no data entry.
  // Everything downstream — COP cost/bushel and break-even, total production,
  // % priced, the sensitivity grid — reads this one function, so the number and
  // the label on it can never disagree across two screens. Before this, a yield
  // hand-keyed once and never revisited was indistinguishable from a live one:
  // Kennedy's wheat divided cost by a typed 71 while the APH said 58.
  function yieldInfo(crop) {
    const { row, acres, aph } = planBasis(crop);
    const keyed   = row?.yield_per_acre    != null ? Number(row.yield_per_acre)    : null;
    const actualBu = row?.actual_production != null ? Number(row.actual_production) : null;
    const actual = (actualBu != null && acres > 0) ? actualBu / acres : null;
    const base = { aph, keyed, actualBu, acres };
    if (actual != null && actual > 0)
      return { ...base, value: Math.round(actual * 10) / 10, basis: 'actual', label: 'Actual', note: 'harvested' };
    if (keyed != null && keyed > 0)
      return { ...base, value: keyed, basis: 'keyed', label: 'Estimated', note: aph ? `keyed — insurance APH ${aph}` : 'keyed on the Crop Plan' };
    return { ...base, value: aph || 0, basis: 'aph', label: 'Estimated', note: aph ? 'crop-insurance APH' : 'no APH on file' };
  }

  function getPlan(crop) {
    const { row, ins, acres, aph, coverage, chopped, grainAcres } = planBasis(crop);
    const y = yieldInfo(crop);
    return {
      acres,
      chopped,          // acres insured as grain that will be chopped — 0 when none entered
      grainAcres,       // acres − chopped: the acres that can deliver BUSHELS
      yield: y.value,
      yieldBasis: y.basis,       // 'actual' | 'keyed' | 'aph'
      aph,
      coverage,
      guaranteeOverride: row?.insurance_guarantee_override != null ? Number(row.insurance_guarantee_override) : null,
      fromInsurance: !!(ins.aph || ins.coverage || ins.acres),
    };
  }

  // Crop-insurance guarantee bushels. For farmers with keyed acres, this is LIVE:
  // insured acres × base APH × base coverage, straight from crop_coverage — the
  // stale manual override does NOT apply. Manual (override, else plan acres×APH×cov)
  // is only the fallback for grain-only / un-keyed farmers.
  function cropGuaranteeBu(crop) {
    const ins = cropInsurance(crop);
    // Per-line, liability-first figure from the resolver — do NOT re-multiply the
    // blended display acres × APH × coverage, which is what over-guaranteed a farm
    // insured at two coverage levels.
    if (ins && ins.guaranteeBu > 0) return ins.guaranteeBu;
    // Only INTENDED acres are on file (pre-planting corn/soy) — model it.
    if (ins && ins.acres > 0 && ins.aph > 0 && ins.coverage > 0) return ins.acres * ins.aph * ins.coverage;
    const p = getPlan(crop);
    if (p.guaranteeOverride != null) return p.guaranteeOverride;
    if (p.acres > 0 && p.aph > 0 && p.coverage > 0) return p.acres * p.aph * p.coverage;
    return null;
  }
  // Expected production = the guarantee with the coverage haircut removed — the full
  // crop a farmer thinks of as "mine", vs the insured guarantee (× coverage %).
  function expectedBushels(crop) {
    const ins = cropInsurance(crop);
    if (ins && ins.expectedBu > 0) return ins.expectedBu;
    if (ins && ins.acres > 0 && ins.aph > 0) return ins.acres * ins.aph;
    const p = getPlan(crop);
    if (p.acres > 0 && p.aph > 0) return p.acres * p.aph;
    return null;
  }

  // Total committed bushels by contract type (Fixed Price / HTA / Basis /
  // Bushels Only). Used both for the donut + the per-type breakdown line.
  function committedByType(crop) {
    const out = {};
    for (const c of cropContracts(crop)) {
      const t = cType(c) || 'Other';
      out[t] = (out[t] || 0) + cBushels(c);
    }
    return out;
  }
  function totalCommitted(crop) {
    return cropContracts(crop).reduce((s, c) => s + cBushels(c), 0);
  }
  // Bushels with a cash price already locked. Fixed Price has it from the
  // jump; HTA has futures+basis when basis is set; Basis contracts count
  // when the cash_price field is populated (sub-pricing complete) — for
  // multi-line basis contracts the user records each priced line as its
  // own row so this works naturally.
  // WHERE THE IMPORTED FILE STATES A PRICED QUANTITY, THAT NUMBER WINS — through
  // the SHARED contractPricedBushels() the contract table and GrainPosition already
  // use, so this card can no longer call a bushel sold that the position engine
  // calls open. It used to test `cash_price > 0` on a Basis row and then count the
  // row's WHOLE contracted quantity: W Cleveland's 104964-P00 is Basis with 0
  // priced, 113.21 unpriced and an elevator cash BID of $5.29, and it read as
  // 113.21 bu sold at $5.29. Those bushels still float with the board, which is
  // the entire difference between a basis contract and a sale.
  //
  // The old type tests stay as the fallback for a row the file cannot answer for —
  // a hand-entered contract, and every HTA (futures locked, basis floating, so the
  // elevator reports 0 priced while the bushels really are committed;
  // estimatedBasisBushels() below is what flags that half as an estimate).
  function pricedBushels(crop) {
    // ⚠ MULTI-LINE, AND THAT IS WHY IT WAS MISSED. The practice sweep replaced
    // every single-line `contracts.filter(c => c.crop === crop)`; these two are
    // written across lines and kept the raw filter, so committed bushels became
    // practice-aware while PRICED bushels did not. On R L Jeffres that printed
    // "Unpriced (committed) -310,000" — a negative bushel count on the card.
    return cropContracts(crop)
      .reduce((s, c) => {
        const stated = contractPricedBushels(c);
        if (stated != null) return s + Math.min(stated, cBushels(c) || stated);
        const t = cType(c);
        if (t === 'Fixed Price') return s + (cFutures(c) > 0 || (c.cash_price != null && Number(c.cash_price) > 0) ? cBushels(c) : 0);
        if (t === 'HTA')         return s + (cFutures(c) > 0 ? cBushels(c) : 0);
        if (t === 'Basis')       return s + (c.cash_price != null && Number(c.cash_price) > 0 ? cBushels(c) : 0);
        return s;
      }, 0);
  }
  function unpricedCommittedBushels(crop) {
    return totalCommitted(crop) - pricedBushels(crop);
  }
  // Average cash price across PRICED bushels. Per the user's spec:
  //   • Fixed Price → cash_price (or futures+basis if cash_price is 0/null)
  //   • HTA         → futures (locked) + basis (when set, else 0)
  //   • Basis       → today's market futures + locked basis
  //   • Bushels Only → not included
  // NOTE on `cash_price=0`: the Schofield CSV uses "0" (not blank) when the
  // cash price isn't set yet on a header line — so we must treat 0 as
  // "unset" and fall through to the computed value, otherwise the avg
  // gets dragged toward $0.
  function avgCashSold(crop) {
    let totalBu = 0, totalDollars = 0;
    const f = getFutures(crop).price;
    const validCash = (v) => v != null && Number(v) > 0 ? Number(v) : null;
    for (const c of cropContracts(crop)) {
      const t = cType(c);
      // Same priced quantity pricedBushels() counts, so the average is taken over
      // exactly the bushels the card calls sold. A Basis row with an elevator cash
      // bid and nothing priced against it weighs 0 and drops out here, instead of
      // dragging the whole contract into the average at a quote.
      const stated = contractPricedBushels(c);
      const bu = stated != null ? Math.min(stated, cBushels(c) || stated) : cBushels(c);
      if (bu <= 0) continue;
      let cashPrice = null;
      const fut = cFutures(c), bas = cBasis(c);
      if (t === 'Fixed Price') {
        cashPrice = validCash(c.cash_price)
          ?? (fut > 0 ? fut + bas : null);
      } else if (t === 'HTA') {
        // basis fixes later — use placeholder from the forward curve until it's set
        cashPrice = fut > 0 ? fut + effBasis(c) : null;
      } else if (t === 'Basis') {
        // Today's market futures + locked basis (Basis contracts have
        // futures floating until each pricing line is locked)
        cashPrice = validCash(c.cash_price) ?? (f > 0 ? f + bas : null);
      }
      if (cashPrice != null && cashPrice > 0) {
        totalBu += bu;
        totalDollars += bu * cashPrice;
      }
    }
    return totalBu > 0 ? totalDollars / totalBu : null;
  }
  // Bushels inside "Avg cash (sold so far)" whose CASH price isn't actually locked
  // yet: an HTA fixes futures but leaves basis floating, so its cash leg is filled
  // from the forward curve and MOVES every day the board does. The blended average
  // is still the right number to show — but it has to say which part is an estimate,
  // or it reads as settled money.
  function estimatedBasisBushels(crop) {
    return cropContracts(crop)
      .filter(c => cType(c) === 'HTA' && cFutures(c) > 0)
      .filter(c => !(c.basis != null && Number(c.basis) !== 0))
      .reduce((s, c) => s + cBushels(c), 0);
  }
  // Hypothetical avg cash if the OPEN bushels (expected − committed) were
  // sold today at the forward-curve weighted price. Bushels-only / unpriced
  // contracts get filled at the curve too.
  function avgCashFullSellout(crop) {
    const expected = totalBu(crop);
    if (expected <= 0) return null;
    const committed = totalCommitted(crop);
    const priced = pricedBushels(crop);
    const open = Math.max(0, expected - committed);
    const unpricedFromContracts = Math.max(0, committed - priced);
    const fwdAvg = fwdAvgPrice(crop) || getFutures(crop).price;
    const pricedAvg = avgCashSold(crop);

    // Total dollars: priced bushels at their avg + unpriced contract bushels at fwd + open at fwd
    const dollars = (priced * (pricedAvg || 0))
                  + (unpricedFromContracts * fwdAvg)
                  + (open * fwdAvg);
    const bushels = priced + unpricedFromContracts + open;
    return bushels > 0 ? dollars / bushels : null;
  }
  // NOT clamped at 100. Committed bushels ABOVE the guarantee are the one exposure
  // this tool exists to catch — selling past the insured bushels means a short crop
  // has to be bought back on a rally to fill a contract you can't deliver — and the
  // old `Math.min(100, …)` rendered 175% and 100% as the same calm "100%".
  // Whether it's a problem is a separate question the card answers with
  // `overSoldNote()`: pre-harvest it's live production risk; post-harvest the
  // bushels exist and there is no cap (Kevin's rule).
  function pctOfExpected(crop) {
    const e = totalBu(crop);
    return e > 0 ? totalCommitted(crop) / e * 100 : null;
  }
  function pctOfGuarantee(crop) {
    const g = cropGuaranteeBu(crop);
    return g > 0 ? totalCommitted(crop) / g * 100 : null;
  }
  // Is this crop year still in the ground? Positive = months before harvest.
  const preHarvest = (crop) => {
    const m = grainMonthsToHarvest(crop, cropYear);
    return m == null ? null : m > 0;
  };
  // The sentence under an over-committed card. Returns null when there's nothing to
  // say. Bushels over the guarantee are stated in BUSHELS, not just a %, because
  // that's the number a farmer has to go buy back.
  function overSoldNote(crop) {
    const g = cropGuaranteeBu(crop);
    if (!(g > 0)) return null;
    const over = totalCommitted(crop) - g;
    if (over <= 0) return null;
    const pre = preHarvest(crop);
    const actual = planRows.find(r => r.crop === crop)?.actual_production;
    if (actual != null && Number(actual) > 0) {
      const overActual = totalCommitted(crop) - Number(actual);
      return overActual > 0
        ? { tone: 'bad', text: `${Math.round(overActual).toLocaleString()} bu sold beyond the ${Math.round(Number(actual)).toLocaleString()} bu actually harvested — those bushels have to come from somewhere.` }
        : { tone: 'ok', text: `Over the insured guarantee, but inside the ${Math.round(Number(actual)).toLocaleString()} bu harvested — the bushels exist.` };
    }
    if (pre === false) return { tone: 'ok', text: `${Math.round(over).toLocaleString()} bu over the insured guarantee — crop is harvested, so there's no production risk on them. Enter actual production to confirm.` };
    return { tone: 'bad', text: `${Math.round(over).toLocaleString()} bu sold beyond the insured guarantee, pre-harvest. RP covers production up to the guarantee; past it a short crop is bought back at the market.` };
  }

  function getCost(crop) {
    // The COP sheet is per COMMODITY. Organic input costs genuinely differ, and
    // this database holds no organic cost series to read them from — so rather
    // than show $0 (which prices the organic row as pure profit) it shows the
    // commodity's cost and the page says which figures are the conventional
    // commodity's. A number labelled as borrowed beats a zero that is not true.
    const row = costRows.find(r => r.crop === grainBaseCrop(crop));
    // Fall back to the COP tool's total (which itself falls back to the per-crop
    // extension-budget pre-fill defaults) so the Crop Plan shows a real starting
    // cost/acre before anything has been entered or saved.
    return (Number(row?.cost_per_acre) || 0) || copTotal(grainBaseCrop(crop));
  }

  // ── Value unpriced bushels off Howlett's basis board ──────────────────────
  // Pick a term (pickup/delivered) + location; each forward month's basis is
  // pre-filled from grain_basis_board (still editable, to price a sale elsewhere).
  const BOARD_CROP_KEY = { Corn:'corn', Soybeans:'soybeans', Wheat:'wheat' };
  // "CN26" (root+monthCode+yy) → sortable month ordinal, to match a posted board bid to a forward-curve month.
  const localContractOrd = (contract) => {
    const m = String(contract || '').match(/([FGHJKMNQUVXZ])(\d{2})$/);
    return m ? (2000 + parseInt(m[2], 10)) * 12 + 'FGHJKMNQUVXZ'.indexOf(m[1]) : null;
  };
  // Posted delivered basis at a local elevator (by source) for a forward-curve month.
  // Exact contract-month match if the board posts it, else the nearest posted month
  // (these boards only quote near-term, so deferred forward months fall back to the
  // closest posted bid — still editable per month afterward).
  function localBoardBasis(source, month) {
    const brd = localBoards[source];
    if (!brd || !brd.length) return null;
    const target = monthLabelToOrd(month);
    const cands = brd
      .map(r => ({ ord: localContractOrd(r.delivery_contract), b: r.delivery_basis }))
      .filter(x => x.ord != null && x.b != null);
    if (!cands.length) return null;
    if (target != null) {
      const exact = cands.find(x => x.ord === target);
      if (exact) return Number(exact.b);
      let best = cands[0];
      for (const x of cands) if (Math.abs(x.ord - target) < Math.abs(best.ord - target)) best = x;
      return Number(best.b);
    }
    return Number(cands[0].b);
  }
  function boardLocations(crop, term) {
    const c = BOARD_CROP_KEY[grainBaseCrop(crop)]; if (!c) return [];
    const seen = [];
    boardRows.filter(r => r.commodity === c && r.term === term)
      .forEach(r => { if (!seen.includes(r.location)) seen.push(r.location); });
    // Add each daily-scraped local elevator for this crop as a Delivered destination when it has bids.
    if (term === 'delivered') {
      LOCAL_ELEVATORS.filter(e => e.crop === crop && (localBoards[e.source] || []).length)
        .forEach(e => { if (!seen.includes(e.label)) seen.push(e.label); });
    }
    return seen;
  }
  // Board basis for a forward-curve month (e.g. "Dec 26") at a location: match the
  // row whose CME contract maps to that month, preferring the matching ship-month.
  function boardBasisFor(crop, term, location, month) {
    const c = BOARD_CROP_KEY[grainBaseCrop(crop)]; if (!c || !location) return null;
    const localE = LOCAL_ELEVATORS.find(e => e.crop === crop && e.label === location);
    if (localE) return localBoardBasis(localE.source, month);
    const abbr = String(month).split(' ')[0].toUpperCase();
    const cands = boardRows.filter(r => r.commodity === c && r.term === term && r.location === location
      && cmeToContractMonth(r.cme_contract) === month);
    if (!cands.length) return null;
    // Prefer the elevator's HARVEST quote for the contract it's quoted against (the
    // natural new-crop basis, e.g. Howlett harvest wheat vs Sep = −0.32), then the
    // row whose ship_month matches the calendar month, then the first candidate.
    const m = cands.find(r => String(r.ship_month).toUpperCase() === 'HARVEST')
      || cands.find(r => String(r.ship_month).toUpperCase().includes(abbr))
      || cands[0];
    return m.basis != null ? Number(m.basis) : null;
  }
  async function applyBoardValuation(crop, term, location) {
    setFwdVal(prev => ({ ...prev, [crop]: location ? { term, location } : (term ? { term, location: null } : null) }));
    if (!location) return;
    const months = liveDeliveryMonths(crop);
    const fills = months.map(m => ({ m, b: boardBasisFor(crop, term, location, m) })).filter(x => x.b != null);
    if (!fills.length) return;
    const defPct = Math.round(100 / months.length);
    // One synchronous batch so the Net column + weighted-avg recompute immediately;
    // persist in the background after.
    setFwdCurveRows(prev => {
      const next = [...prev];
      for (const { m, b } of fills) {
        const idx = next.findIndex(r => r.crop === crop && r.delivery_month === m);
        const pct = idx >= 0 ? next[idx].pct_allocation : defPct;
        const row = { farmer_id: selectedFarmerId, crop_year: cropYear, crop, delivery_month: m, pct_allocation: pct, basis: b, futures_price: null };
        if (idx >= 0) next[idx] = row; else next.push(row);
      }
      return next;
    });
    setFwdSave({ status: 'saving' });
    try {
      for (const { m, b } of fills) {
        const existing = fwdCurveRows.find(r => r.crop === crop && r.delivery_month === m);
        await grainSaveFwdCurve(selectedFarmerId, cropYear, crop, m, existing ? existing.pct_allocation : defPct, b, null);
      }
      setFwdSave({ status: 'ok', at: Date.now() });
    } catch { setFwdSave({ status: 'err' }); }
  }

  // Delivery months for the curve, minus any whose futures contract has already
  // rolled past for this marketing year (grain contracts go into delivery at the
  // start of their month, so drop the current month and earlier). Falls back to the
  // full list if that would empty the curve.
  function liveDeliveryMonths(crop) {
    const months = GRAIN_DELIVERY_MONTHS[cropYear]?.[grainBaseCrop(crop)] || [getFutures(crop).month];
    const now = new Date();
    const nowOrd = now.getFullYear() * 12 + now.getMonth();   // getMonth() is 0-11, matching monthLabelToOrd
    const live = months.filter(m => { const o = monthLabelToOrd(m); return o == null || o > nowOrd; });
    return live.length ? live : months;
  }
  function getFwdCurve(crop) {
    const months = liveDeliveryMonths(crop);
    return months.map(m => {
      const row = fwdCurveRows.find(r => r.crop === crop && r.delivery_month === m);
      // Each month shows its OWN live daily settle (not one flat crop #). Stored
      // futures_price is intentionally ignored below so the curve can never freeze.
      const liveMonth = liveSettle(grainBaseCrop(crop), m);
      return {
        month: m,
        pct: Number(row?.pct_allocation) || (row ? 0 : Math.round(100 / months.length)),
        basis: row?.basis != null ? Number(row.basis) : -0.25,
        // Futures ALWAYS track this delivery month's live daily settle (falling back
        // to the live new-crop mark). We deliberately ignore any stored futures_price
        // so the curve updates every trading day and can never be frozen by a % / basis
        // edit. Basis + % are the only user-controlled inputs.
        futures: liveMonth != null ? liveMonth : getFutures(crop).price,
      };
    });
  }

  // Read fields with new-schema priority, fall back to legacy keys.
  const cBushels = (c) => Number(c.bushels_contracted ?? c.bushels) || 0;
  const cType    = (c) => c.contract_type || c.type;
  const cFutures = (c) => Number(c.futures_price ?? c.futures) || 0;
  const cBasis   = (c) => Number(c.basis) || 0;

  // An HTA locks futures but fixes basis later. Until the grower sets basis we
  // price it with a PLACEHOLDER basis pulled from the forward price curve, so the
  // position still has a realistic cash number — flagged as an estimate in the UI.
  // Treat null/blank AND an exact 0 as "not yet fixed" (a real locked HTA basis is
  // essentially never exactly 0.00, and unset imports arrive as 0 or blank).
  const rawBasis = (c) => (c.basis === null || c.basis === undefined || c.basis === '' ? null : Number(c.basis));
  const htaBasisUnfixed = (c) => cType(c) === 'HTA' && (rawBasis(c) == null || rawBasis(c) === 0);
  // Placeholder basis for an unfixed HTA, taken from the forward price curve for
  // the SPECIFIC month that matches the contract (the grower's ask). Resolution:
  // exact curve month → nearest curve month by calendar distance → %-weighted-avg
  // curve basis (only if the contract's month can't be determined at all). Returns
  // { basis, month } so the UI can show which curve month it borrowed.
  function htaPlaceholder(c) {
    const curve = getFwdCurve(c.crop);
    if (!curve.length) return { basis: -0.25, month: null };
    const target = contractTargetMonth(c);
    if (target) {
      const exact = curve.find(r => r.month === target);
      if (exact) return { basis: exact.basis, month: exact.month };
      const tOrd = monthLabelToOrd(target);
      if (tOrd != null) {
        let best = null, bestD = Infinity;
        for (const r of curve) {
          const o = monthLabelToOrd(r.month);
          if (o == null) continue;
          const d = Math.abs(o - tOrd);
          if (d < bestD) { bestD = d; best = r; }
        }
        if (best) return { basis: best.basis, month: best.month };
      }
    }
    const w = curve.reduce((s, r) => s + (Number(r.pct) || 0), 0);
    const basis = w > 0 ? curve.reduce((s, r) => s + (Number(r.pct) || 0) * r.basis, 0) / w
                        : curve.reduce((s, r) => s + r.basis, 0) / curve.length;
    return { basis, month: null };
  }
  // Effective basis used to price a contract — placeholder for unfixed HTAs, else the entered basis.
  const effBasis = (c) => htaBasisUnfixed(c) ? htaPlaceholder(c).basis : cBasis(c);

  function netPrice(c) {
    const f = getFutures(c.crop).price;
    if (cType(c) === 'Basis')        return f + cBasis(c);
    if (cType(c) === 'HTA')          return cFutures(c) + effBasis(c);   // placeholder basis if not yet fixed
    if (cType(c) === 'Bushels Only') return f;       // unpriced, value at current futures
    return cFutures(c) + cBasis(c);                  // Fixed Price
  }

  // Expected production. Falls back to APH when no Est. Yield is entered, so the
  // whole expected-production side (% of expected, unsold, Avg-if-full-sellout)
  // still computes for a farmer who has acres + insurance APH but hasn't keyed a
  // yield. Matches the COP tool's yld = plan.yield || plan.aph. Est. Yield wins.
  // TOTAL PRODUCTION. When actual bushels are on file that IS the number — do not
  // rebuild it from acres × yield, because `yield` is the actual rounded to 1dp and
  // multiplying it back out reintroduces an error the grower never made (Sullivan's
  // 36,200 reported comes back as 36,203). It also lets a total stand before acres
  // are keyed at all, which is the point of taking the entry in bushels.
  function totalBu(crop) {
    const y = yieldInfo(crop);
    if (y.actualBu != null && y.actualBu > 0) return y.actualBu;
    // GRAIN acres, not planted acres — a chopped acre grows a crop but delivers no
    // bushels, so it belongs in cost/acre (which still runs off `acres`) and not in
    // the production a contract can be filled from. Reported ACTUAL bushels still
    // win outright above: once the crop is off, the number is the number.
    const p = getPlan(crop); return p.grainAcres * (p.yield || p.aph || 0);
  }
  // THE COMMODITY's production, both practices. A corn contract is filled from
  // corn; the elevator does not know which field it came off, and neither do
  // we. Measuring % committed against the conventional half alone would report
  // W Cleveland as roughly twice as sold as he is.
  function commodityTotalBu(crop) { return totalBu(crop); }
  // The plan rows are per COMMODITY, so getPlan already carries both practices'
  // acres and there is nothing to roll up. Kept as the one place that would
  // have to change if a per-practice plan row is ever introduced.
  function commodityAcres(crop) { return getPlan(crop).acres; }
  function soldBu(crop)  { return cropContracts(crop).reduce((s, c) => s + cBushels(c), 0); }
  function unsoldBu(crop) { const t = commodityTotalBu(crop); return t ? Math.max(0, t - soldBu(crop)) : null; }
  function pctSold(crop) { const t = commodityTotalBu(crop); return t ? Math.min(100, soldBu(crop) / t * 100) : 0; }

  function fwdAvgPrice(crop) {
    const curve = getFwdCurve(crop);
    let totalPct = 0, wSum = 0;
    curve.forEach(r => { totalPct += r.pct; wSum += r.pct * (r.futures + r.basis); });
    return totalPct ? wSum / totalPct : null;
  }

  function cropRevenue(crop) {
    const u = unsoldBu(crop) || 0;
    const contracted = cropContracts(crop).reduce((s, c) => s + cBushels(c) * netPrice(c), 0);
    const unsoldVal  = u * (fwdAvgPrice(crop) || getFutures(crop).price);
    return contracted + unsoldVal;
  }

  function cropExpenses(crop) { return commodityAcres(crop) * getCost(crop); }
  function cropGP(crop)       { return cropRevenue(crop) - cropExpenses(crop); }
  function cropGPA(crop)      { const a = getPlan(crop).acres; return a ? cropGP(crop) / a : null; }
  const hasCosts = () => marketingCrops.some(c => getCost(c) > 0);

  async function handleSaveContract(form) {
    if (!selectedFarmerId) return;
    // A hand-entered contract and an imported one cannot see each other: the
    // import's dedup key is (contract_number, pricing_line_id) and a typed row
    // has no pricing line, so `103961|` never matches `103961|103961-P00`, and
    // the partial unique index only covers source='csv_import'. That is how
    // Meisenzahl's 103961 and 104502 came to exist twice — 7,000 bu of soybeans
    // counted twice against the forward-sell guarantee. This is the one place
    // that can still catch it, so it asks rather than silently inserting.
    const newNum = String(form.contract_number || '').trim();
    if (!(form.id && form.id.length > 10) && newNum) {
      const clash = contracts.filter(c => String(c.contract_number || '').trim().toLowerCase() === newNum.toLowerCase());
      if (clash.length) {
        const what = clash.map(c => `${c.crop} · ${Number(cBushels(c) || 0).toLocaleString()} bu · ${c.buyer || 'buyer not recorded'}${c.source === 'csv_import' ? ' (from the elevator export)' : ''}`).join('\n  ');
        if (!window.confirm(`Contract ${newNum} is already on this farm:\n  ${what}\n\nAdding it again double-counts those bushels against the crop's guarantee. Add anyway?`)) return;
      }
    }
    setSaving(true);
    try {
      const id = await grainSaveContract(selectedFarmerId, cropYear, form);
      if (form.id && form.id.length > 10) {
        setContracts(prev => prev.map(c => c.id === form.id ? { ...form, id } : c));
      } else {
        setContracts(prev => [{ ...form, id }, ...prev]);
      }
      setModal(null); setEditingContract(null);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  // Toggle a contract's "filled" (delivered/complete) flag. Manual marker,
  // independent of the CSV bushels_delivered; a filled contract still counts as
  // a completed sale. RLS allows the owning farmer + admins to update.
  async function handleToggleFilled(c) {
    const next = !c.filled;
    setContracts(prev => prev.map(x => x.id === c.id ? { ...x, filled: next, filled_at: next ? new Date().toISOString() : null } : x));
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/grain_contracts?id=eq.${c.id}`, {
        method: 'PATCH', headers: { ...getAuthHeaders(), Prefer: 'return=minimal' },
        body: JSON.stringify({ filled: next, filled_at: next ? new Date().toISOString() : null }),
      });
      if (!res.ok) throw new Error(await res.text());
    } catch (e) { setError(e.message); /* revert on failure */ setContracts(prev => prev.map(x => x.id === c.id ? { ...x, filled: c.filled, filled_at: c.filled_at } : x)); }
  }

  async function handleDeleteContract(id) {
    if (!window.confirm('Delete this contract?')) return;
    setSaving(true);
    try {
      await grainDeleteContract(id);
      setContracts(prev => prev.filter(c => c.id !== id));
      setModal(null); setEditingContract(null);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  // Update one or more plan fields for a crop. Fields supported:
  // acres, yield_per_acre (existing), plus aph_yield, coverage_level,
  // insurance_guarantee_override (new).
  async function handlePlanChange(crop, fieldOrUpdates, value) {
    const updates = typeof fieldOrUpdates === 'string'
      ? { [fieldOrUpdates]: value === '' ? null : Number(value) }
      : fieldOrUpdates;
    // Translate UI field name `yield` → DB column `yield_per_acre`
    if ('yield' in updates) { updates.yield_per_acre = updates.yield; delete updates.yield; }
    setPlanRows(prev => {
      const idx = prev.findIndex(r => r.crop === crop);
      const cur = idx >= 0 ? prev[idx] : { farmer_id: selectedFarmerId, crop_year: cropYear, crop };
      const merged = { ...cur, ...updates };
      return idx >= 0 ? prev.map((r, i) => i === idx ? merged : r) : [...prev, merged];
    });
    grainSavePlan(selectedFarmerId, cropYear, crop, updates).catch(() => {});
  }

  // Writes the Crop Plan's cost/acre. The save REJECTS on failure so persistCOP can
  // report it — a silently-dropped write here is what leaves the Crop Plan costing
  // the crop at a number the COP screen no longer shows.
  async function handleCostChange(crop, value) {
    const cpa = Number(value) || 0;
    setCostRows(prev => {
      const idx = prev.findIndex(r => r.crop === crop);
      const row = { farmer_id: selectedFarmerId, crop_year: cropYear, crop, cost_per_acre: cpa };
      return idx >= 0 ? prev.map((r, i) => i === idx ? row : r) : [...prev, row];
    });
    await grainSaveCost(selectedFarmerId, cropYear, crop, cpa);
  }

  // ── Cost of Production tool ────────────────────────────────────────────────
  function getCOP(crop) {
    const row = copRows.find(r => r.crop === crop);
    return { mode: row?.mode || 'detailed', items: row?.line_items || {}, hasRow: !!row };
  }
  // ── COP rules live in src/copRules.js — do not re-implement them here ─────
  // The economics moved out of this component in Farm Economics Increment 2A so
  // the SILAS shadow estimator calls the SAME rules rather than growing a second
  // set. What stays here is DATA ACQUISITION — resolving this farmer's real
  // producer-paid crop-insurance premium — plus thin delegations that keep the
  // original signatures, so not one of the call sites in this file had to change.
  //
  // Farmer's ACTUAL crop-insurance cost per acre. The CALCULATION is
  // `insurancePremiumPerAcreFrom` in copRules; this half is only "where do the
  // rows come from". `cropInsurance(crop)` is passed as a THUNK because it runs
  // the liability-first guarantee resolver — it must stay as lazy as it was.
  function insurancePremiumPerAcre(crop) {
    if (!selectedFarmer) return null;
    return insurancePremiumPerAcreFrom({
      crop,
      approvedYields: selectedFarmer.approvedYields,
      scoEcoEndorsements: selectedFarmer.scoEcoEndorsements,
      resolveFallbackAcres: () => { const ci = cropInsurance(crop); return (ci && ci.acres) || 0; },
    });
  }
  // Delegations to the canonical rules. Each supplies the one piece of context the
  // pure layer needs — this farmer's premium, lazily — so no call site can drop it.
  // Registered in tools/check-shared-rules.js. Do not inline a rule back into a body.
  // A function DECLARATION, not a const arrow: these wrappers are hoisted and are
  // reached through `getCost` -> `copTotal`, so a const here would sit in the
  // temporal dead zone for any caller that runs earlier in the component body.
  // That exact shape shipped a render crash on 2026-08-01 (see check-tdz.js).
  function _prem(crop) { return () => insurancePremiumPerAcre(crop); }
  function copVal(mode, crop, items, key) { return copValRule(mode, crop, items, key, _prem(crop)); }
  function copDefault(mode, crop, key) { return copDefaultRule(mode, crop, key, _prem(crop)); }
  function copCats(mode, crop) { return copCatsRule(mode, crop); }
  function copPasses(items, key) { return copPassesRule(items, key); }
  function copFieldOpsTotal(mode, crop, items) { return copFieldOpsTotalRule(mode, crop, items, _prem(crop)); }
  function copMachMethod(mode, crop, items) { return copMachMethodRule(mode, crop, items, _prem(crop)); }
  function copLine(mode, crop, items, cat) { return copLineRule(mode, crop, items, cat, _prem(crop)); }
  function copSigned(mode, crop, items, cat) { return copSignedRule(mode, crop, items, cat, _prem(crop)); }
  function copSum(mode, crop, items) { return copSumRule(mode, crop, items, _prem(crop)); }
  // The by-product credit already inside copSum, and the gross it implies, so a
  // caller can show gross -> credit -> net instead of one opaque number. Kept
  // wired to the rule (and to check-shared-rules) after the Profitability tab
  // that used it was removed 2026-08-17 — copSum is still NET on every path, so
  // any future caller must not subtract the credit a second time.
  function copCreditTotal(mode, crop, items) { return copCreditTotalRule(mode, crop, items, _prem(crop)); }
  function copGrossTotal(mode, crop, items) { return copGrossTotalRule(mode, crop, items, _prem(crop)); }
  function copTotal(crop) {
    const { mode, items } = getCOP(crop);
    return copSum(mode, crop, items);
  }
  // Save the row AND report the outcome. This used to be fire-and-forget
  // (`.catch(() => {})`), which is how the screen and the database could disagree:
  // the total re-rendered from local state whether or not the write landed, so a
  // failed save left the Crop Plan running on the OLD cost/acre with nothing said.
  // Same swallow the forward curve had, same fix — surface it.
  async function persistCOP(crop, mode, items, total) {
    setCopRows(prev => {
      const idx = prev.findIndex(r => r.crop === crop);
      const row = { farmer_id: selectedFarmerId, crop_year: cropYear, crop, mode, line_items: items, total_per_acre: total };
      return idx >= 0 ? prev.map((r, i) => i === idx ? row : r) : [...prev, row];
    });
    setCopSave({ status: 'saving' });
    try {
      await grainSaveCOP(selectedFarmerId, cropYear, crop, mode, items, total);
      // Feed the Crop Plan cost/acre with the computed total — only once the COP
      // row itself is down, so the two can't land out of step.
      await handleCostChange(crop, total);
      setCopSave({ status: 'ok', at: Date.now() });
    } catch {
      setCopSave({ status: 'err' });
    }
  }
  function handleCOPChange(crop, key, value) {
    const { mode, items } = getCOP(crop);
    const next = { ...items };
    if (value === '' || value == null) delete next[key]; else next[key] = Number(value);
    persistCOP(crop, mode, next, copSum(mode, crop, next));
  }
  function handleCOPMode(crop, mode) {
    const { items } = getCOP(crop);
    persistCOP(crop, mode, items, copSum(mode, crop, items));
  }
  // Machinery accounting method. Stored as a string, so it goes through its own
  // handler rather than handleCOPChange's Number() coercion.
  function handleCOPMachMethod(crop, method) {
    const { mode, items } = getCOP(crop);
    const next = { ...items, __machMethod: method };
    persistCOP(crop, mode, next, copSum(mode, crop, next));
  }

  async function handleFwdCurveChange(crop, month, field, value) {
    const curve = getFwdCurve(crop);
    const cur = curve.find(r => r.month === month) || { month, pct: 0, basis: -0.25 };
    let parsed = Number(value) || 0;
    // Clamp each month to 0–100%. We deliberately DON'T cap the cross-month total
    // at 100 here: the old hard cap (allowed = 100 − otherSum) made raising one
    // month — or typing into a blank month when the others already summed to 100 —
    // silently snap right back to the previous value, so the curve looked frozen.
    // The running total is shown in the header (turns red past 100%), and the
    // weighted-avg price normalizes by the total, so free editing is safe.
    if (field === 'pct') parsed = Math.min(100, Math.max(0, parsed));
    const next = { ...cur, [field]: parsed };
    // Persist ONLY the user-controlled inputs (% allocation + basis). We store
    // futures_price = null so the futures leg always resolves live from the daily
    // settle in getFwdCurve — editing % or basis can no longer freeze a month's
    // futures at that day's value (the old snapshot bug).
    setFwdCurveRows(prev => {
      const idx = prev.findIndex(r => r.crop === crop && r.delivery_month === month);
      const row = { farmer_id: selectedFarmerId, crop_year: cropYear, crop, delivery_month: month, pct_allocation: next.pct, basis: next.basis, futures_price: null };
      return idx >= 0 ? prev.map((r, i) => i === idx ? row : r) : [...prev, row];
    });
    // Surface the save outcome instead of swallowing it — the user asked to be sure
    // basis/% actually persist. On failure we flag it so a silent network blip can't
    // leave a typed value unsaved without warning.
    setFwdSave({ status: 'saving' });
    try {
      await grainSaveFwdCurve(selectedFarmerId, cropYear, crop, month, next.pct, next.basis, null);
      setFwdSave({ status: 'ok', at: Date.now() });
    } catch {
      setFwdSave({ status: 'err' });
    }
    return parsed;
  }

  async function handleSaveFutures(updates) {
    setFuturesRef(prev => {
      const next = [...prev];
      updates.forEach(u => {
        const idx = next.findIndex(r => r.crop === u.crop);
        const row = { crop_year: cropYear, crop: u.crop, price: u.price, ref_month: u.month };
        if (idx >= 0) next[idx] = row; else next.push(row);
      });
      return next;
    });
    await Promise.all(updates.map(u => grainSaveFuturesRef(cropYear, u.crop, u.price, u.month)));
    setModal(null);
  }

  // Key a contract row by (contract_number, pricing_line_id) — the natural
  // identity from the Bushel/Schofield export. Used by the import delta to
  // decide UPDATE vs INSERT and to dedupe across re-uploads.
  const grainDedupKey = (r) => `${r.contract_number || ''}|${r.pricing_line_id || ''}`;

  function handleCSVFile(file) {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const parsed = parseGrainCSV(text);
      setImportPreview(parsed);
      setImportDelta(null);

      // Default the buyer from what the FILE says first, and only then from what
      // this farm's existing imported contracts say. Both are labelled in the UI;
      // neither is silent. A farm whose contracts disagree on the buyer gets no
      // default at all rather than the first one that sorts.
      const stated = grainBuyerFromRows(parsed);
      if (stated) setImportBuyer({ value: stated, from: 'file', locations: grainFileLocations(parsed) });
      else {
        const known = [...new Set(contracts.filter(c => c.source === 'csv_import')
          .map(c => (c.buyer || '').trim()).filter(Boolean))];
        const locations = grainFileLocations(parsed);
        // The book's guess is only offered when the file does not contradict it.
        // See grainBookBuyerFits — this is the gate that would have stopped six
        // Edgewood contracts landing under "Howlett Farms".
        if (known.length === 1 && grainBookBuyerFits(parsed, known[0])) {
          setImportBuyer({ value: known[0], from: 'book', locations });
        } else if (known.length === 1) {
          setImportBuyer({ value: '', from: 'book-conflict', rejected: known[0], locations });
        } else {
          setImportBuyer({ value: '', from: null, locations });
        }
      }

      // Build the delta against existing csv_import rows for this farmer
      // so the preview can show "will update / will insert" counts and
      // confirmImport knows which rows are PATCHes vs new inserts.
      if (!selectedFarmerId || !parsed.length) return;
      try {
        const farmerDbIds = selectedFarmer?.allFarmerDbIds || [selectedFarmerId];
        // Fetch ALL sources so we can protect hand-edited rows (source='manual')
        // from being overwritten, deleted, OR re-inserted as a CSV duplicate.
        const existing = await sbFetch('grain_contracts', {
          filters: [`farmer_id=in.(${farmerDbIds.join(',')})`],
          select: 'id,contract_number,pricing_line_id,source,crop,crop_year,buyer',
        });
        const existingByKey = new Map();   // csv_import rows → update/stale targets
        const protectedKeys = new Set();   // manual (hand-edited/added) → importer skips
        (existing || []).forEach(r => {
          if (r.source === 'csv_import') existingByKey.set(grainDedupKey(r), r.id);
          else protectedKeys.add(grainDedupKey(r));
        });
        const incomingKeys = new Set(parsed.map(grainDedupKey));
        let updateCount = 0, protectedCount = 0;
        for (const r of parsed) {
          const k = grainDedupKey(r);
          if (protectedKeys.has(k)) protectedCount++;
          else if (existingByKey.has(k)) updateCount++;
        }
        // Stale = csv_import rows not in this upload, narrowed by BOTH scopes —
        // (crop, crop_year) AND buyer. See grainImportStaleIds. The row list is
        // kept rather than the answer, because the buyer is still editable in
        // the panel: the count on the button has to move when it changes.
        setImportDelta({
          existingByKey,
          protectedKeys,
          existingRows: existing || [],
          updateCount,
          protectedCount,
          insertCount: parsed.length - updateCount - protectedCount,
        });
      } catch {
        // If the lookup fails, fall back to treating everything as new —
        // the partial unique index on grain_contracts will still reject
        // any true duplicate at the DB level. No stale removal in this
        // path (we don't know what to remove).
        setImportDelta({ existingByKey: new Map(), existingRows: [], updateCount: 0, insertCount: parsed.length });
      }
    };
    reader.readAsText(file);
  }

  async function confirmImport() {
    if (!importPreview || !selectedFarmerId) return;
    // One file is one elevator, and the file does not name it. Refuse rather than
    // write a contract whose counterparty is blank — that is the state 103961 and
    // 104502 were in when they got entered a second time by hand.
    const buyer = (importBuyer.value || '').trim();
    if (!buyer) { setError('Enter the buyer (elevator) this export came from before importing.'); return; }
    setSaving(true);
    try {
      // Each preview row already carries its own derived crop_year from
      // parseGrainCSV (based on delivery date / futures symbol). Honor
      // that — fall back to the dropdown selection only when derivation
      // failed (no delivery date, no futures symbol).
      const incomingRows = importPreview.map(r => ({
        ...buildGrainContractPayload(selectedFarmerId, r.crop_year || cropYear, { ...r, buyer }),
        source: 'csv_import',
        _key: grainDedupKey(r),
      }));

      // Partition into updates (existing pricing line — PATCH by id) and
      // inserts (new pricing line — POST). The dedup map was built when
      // the file was previewed; refresh if it's missing (shouldn't happen
      // in normal flow but keeps confirm robust against state resets).
      let existingByKey = importDelta?.existingByKey;
      let protectedKeys = importDelta?.protectedKeys;
      let existingRows = importDelta?.existingRows;
      if (!existingByKey || !protectedKeys || !existingRows) {
        const farmerDbIds = selectedFarmer?.allFarmerDbIds || [selectedFarmerId];
        const existing = await sbFetch('grain_contracts', {
          filters: [`farmer_id=in.(${farmerDbIds.join(',')})`],
          // `buyer` is not optional here — grainImportStaleIds refuses to remove
          // a row it cannot attribute, so a select that omits the column would
          // silently make every removal a no-op.
          select: 'id,contract_number,pricing_line_id,source,crop,crop_year,buyer',
        });
        existingByKey = new Map();
        protectedKeys = new Set();
        existingRows = existing || [];
        existingRows.forEach(r => {
          if (r.source === 'csv_import') existingByKey.set(grainDedupKey(r), r.id);
          else protectedKeys.add(grainDedupKey(r));
        });
      }

      const toUpdate = [];
      const toInsert = [];
      for (const row of incomingRows) {
        if (protectedKeys.has(row._key)) continue;   // hand-edited → leave it alone
        const id = existingByKey.get(row._key);
        const { _key, ...payload } = row;
        if (id) toUpdate.push({ id, payload });
        else toInsert.push(payload);
      }

      // PATCH 25 at a time to avoid hammering PostgREST with hundreds of
      // parallel requests.
      for (let i = 0; i < toUpdate.length; i += 25) {
        const chunk = toUpdate.slice(i, i + 25);
        await Promise.all(chunk.map(u =>
          fetch(`${SUPABASE_URL}/rest/v1/grain_contracts?id=eq.${u.id}`, {
            method: 'PATCH',
            headers: { ...getAuthHeaders(), Prefer: 'return=minimal' },
            body: JSON.stringify(u.payload),
          })
        ));
      }

      if (toInsert.length) await grainBulkInsertContracts(toInsert);

      // Remove rows that exist in the DB as csv_import but weren't in this
      // upload — the export is the source of truth FOR ITS OWN ELEVATOR, so a
      // contract that dropped off it (voided, paid out, closed) is removed here
      // while another buyer's contracts in the same crop-year are left alone.
      // Manual rows are protected by the source filter and by the partial
      // unique index. Deletes go through PostgREST's IN filter, chunked to keep
      // the URL under length limits.
      //
      // Derived HERE from the buyer that was actually confirmed, not from
      // whatever was typed when the file was first read — the same call the
      // preview count runs, so the button's number is the number that goes.
      const { staleIds } = grainImportStaleIds(existingRows, importPreview, buyer);
      for (let i = 0; i < staleIds.length; i += 50) {
        const chunk = staleIds.slice(i, i + 50);
        await sbDelete('grain_contracts', `id=in.(${chunk.join(',')})`);
      }

      // Reload from DB rather than appending (the imported rows may be on
      // crop years OTHER than the currently-selected one, so showing them
      // immediately would be misleading)
      const fresh = await grainLoadAll(grainOwnerIds, cropYear);
      setContracts(fresh.contracts);
      setImportPreview(null);
      setImportDelta(null);
      setImportBuyer({ value: '', from: null });
      setTab('contracts');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  // Recalculates crop_year for every contract on this farmer using the
  // same rules as parseGrainCSV. Useful when contracts were imported
  // before the per-row crop_year derivation was added (everything
  // landed on the dropdown-selected year). Iterates client-side because
  // we don't have a server function — 100+ rows finishes in 2-3s.
  async function handleRecalcCropYears() {
    if (!selectedFarmerId) return;
    if (!window.confirm('Recalculate crop years for ALL contracts on this farmer? Uses delivery date or futures month.')) return;
    setSaving(true);
    try {
      const farmerDbIds = selectedFarmer?.allFarmerDbIds || [selectedFarmerId];
      const idFilter = farmerDbIds.join(',');
      const rows = await sbFetch('grain_contracts', {
        filters: [`farmer_id=in.(${idFilter})`],
        select: 'id,crop,crop_year,futures_symbol,delivery_start,ship_month',
      });
      const updates = (rows || [])
        .map(r => {
          const newY = deriveContractCropYear({
            ship_month: r.ship_month,
            futures_symbol: r.futures_symbol,
            delivery_start: r.delivery_start,
            crop: r.crop,
          });
          if (!newY || newY === r.crop_year) return null;
          return { id: r.id, crop_year: newY };
        })
        .filter(Boolean);
      if (!updates.length) {
        alert('All contracts already have correct crop years.');
        return;
      }
      // Batch PATCH — 25 concurrent at a time
      const chunks = [];
      for (let i = 0; i < updates.length; i += 25) chunks.push(updates.slice(i, i + 25));
      for (const chunk of chunks) {
        await Promise.all(chunk.map(u =>
          fetch(`${SUPABASE_URL}/rest/v1/grain_contracts?id=eq.${u.id}`, {
            method: 'PATCH',
            headers: { ...getAuthHeaders(), Prefer: 'return=minimal' },
            body: JSON.stringify({ crop_year: u.crop_year }),
          })
        ));
      }
      alert(`Updated ${updates.length} contract${updates.length === 1 ? '' : 's'}.`);
      const fresh = await grainLoadAll(grainOwnerIds, cropYear);
      setContracts(fresh.contracts);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  function exportCSV() {
    // `crop_program` and `delivery_location` ride here too: an export that drops
    // them makes a Plenish contract look conventional and a Star-of-the-West
    // delivery look like a Howlett-yard delivery, which is the whole point of
    // capturing either one.
    const rows = [['crop_year', 'contract_number', 'pricing_line_id', 'crop', 'crop_program', 'contract_type', 'buyer', 'delivery_location', 'bushels_contracted', 'bushels_priced', 'bushels_delivered', 'futures_symbol', 'futures_month', 'futures_price', 'basis', 'cash_price', 'net_price', 'delivery_start', 'delivery_end', 'notes']];
    contracts.forEach(c => {
      const np = netPrice(c);
      rows.push([
        cropYear, c.contract_number || '', c.pricing_line_id || '', c.crop, c.crop_program || '', cType(c), c.buyer || '', c.location || '',
        cBushels(c), c.bushels_priced ?? '', c.bushels_delivered ?? '',
        c.futures_symbol || '', c.futures_month || c.fmonth || '',
        Number(cFutures(c)).toFixed(2), Number(cBasis(c)).toFixed(2),
        c.cash_price != null ? Number(c.cash_price).toFixed(2) : '',
        np.toFixed(2),
        c.delivery_start || c.dstart || '', c.delivery_end || c.dend || '',
        c.notes || '',
      ]);
    });
    const csv = rows.map(r => r.map(v => '"' + String(v || '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `grain_${selectedFarmer?.name?.replace(/\s+/g, '_') || 'export'}_${cropYear}.csv`; a.click();
  }

  const S = {
    card:      { background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, padding: '18px 22px', marginBottom: 14 },
    cardSm:    { background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, padding: '14px 18px' },
    label:     { display: 'block', fontSize: 10, fontWeight: 800, color: '#4a6e4a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 },
    input:     { width: '100%', padding: '8px 11px', border: '1px solid #c8d8c0', borderRadius: 7, fontSize: 13, background: '#fff', color: '#1a2e1a', fontFamily: 'inherit' },
    inputSm:   { width: '100%', padding: '3px 6px', border: '1px solid #c8d8c0', borderRadius: 5, fontSize: 11, background: '#fff', color: '#1a2e1a', fontFamily: 'inherit' },
    btnGreen:  { padding: '8px 16px', background: '#2d4a2d', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
    btnOut:    { padding: '8px 16px', background: '#fff', color: '#2d4a2d', border: '1px solid #c8d8c0', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
    btnSmOut:  { padding: '4px 10px', background: '#fff', color: '#4a6e4a', border: '1px solid #c8d8c0', borderRadius: 5, fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
    btnRed:    { padding: '5px 10px', background: '#fdecea', color: '#c0392b', border: '1px solid #e0a0a0', borderRadius: 5, fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
    tag:       (on) => ({ padding: '3px 11px', borderRadius: 20, fontSize: 11, cursor: 'pointer', fontWeight: 700, border: `1px solid ${on ? '#2d4a2d' : '#c8d8c0'}`, background: on ? '#2d4a2d' : '#fff', color: on ? '#fff' : '#60735f' }),
    th:        { padding: '8px 12px', fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#4a6e4a', textTransform: 'uppercase', borderBottom: '1px solid #dde8d8', background: '#f4f8f0', textAlign: 'left', whiteSpace: 'nowrap' },
    td:        { padding: '8px 12px', fontSize: 12, borderBottom: '1px solid #f0f4f0', verticalAlign: 'middle' },
    typeBadge: (type) => {
      const palette = {
        'Fixed Price':  { bg: '#eaf4e4', fg: '#2d5a10' },
        'HTA':          { bg: '#e6f1fb', fg: '#1a4a7a' },
        'Basis':        { bg: '#fef4e8', fg: '#7a4a00' },
        'Bushels Only': { bg: '#f4ecff', fg: '#5a2a8a' },
        'Cash':         { bg: '#eaf4e4', fg: '#2d5a10' },
      };
      const p = palette[type] || { bg: '#f4f4f4', fg: '#5a5a5a' };
      return { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 800, background: p.bg, color: p.fg, whiteSpace: 'nowrap' };
    },
  };

  // The app only loads CURRENT_CROP_YEAR coverage into the farmer object, so the
  // guarantee for a DIFFERENT selected crop year (e.g. 2027 wheat) must be fetched
  // fresh — and stays BLANK until that year's coverage is actually keyed, then
  // populates. Priced bushels are always the selected year's grain_contracts.
  const [futureCovGuar, setFutureCovGuar] = useState({});  // { Wheat|Corn|Soybeans: {guaranteeBu, expectedBu} } for cropYear ≠ CURRENT
  useEffect(() => {
    let alive = true;
    if (!selectedFarmerId || cropYear === CURRENT_CROP_YEAR) { setFutureCovGuar({}); return; }
    (async () => {
      const ids = grainOwnerIds;
      const pols = await sbFetch('policies', { select: 'id', filters: [`farmer_id=in.(${ids.join(',')})`, `crop_year=eq.${cropYear}`] }).catch(() => []);
      if (!alive) return;
      if (!pols.length) { setFutureCovGuar({ _keyed: false }); return; }
      const pids = pols.map(p => p.id);
      const cc = await sbFetch('crop_coverage', { select: 'county,crop,type,type_code,practice_code,plan,aph_yield,coverage_level,acres,pp_acres,liability,price_election,price_election_pct', filters: [`policy_id=in.(${pids.join(',')})`] }).catch(() => []);
      if (!alive) return;
      setFutureCovGuar({
        _keyed: true,
        Wheat: grainBushelsFromCoverage(cc, 'Wheat'),
        Corn: grainBushelsFromCoverage(cc, 'Corn'),
        Soybeans: grainBushelsFromCoverage(cc, 'Soybeans'),
      });
    })();
    return () => { alive = false; };
  }, [selectedFarmerId, cropYear, selectedFarmer]);

  // Delivery tickets (scale tickets) for the farm — all years; attributed to a crop
  // year by delivery date. Powers contract-fill + open-storage (delivered − contracted).
  const [tickets, setTickets] = useState([]);
  const [ticketImport, setTicketImport] = useState(null);   // {status, msg}
  useEffect(() => {
    let alive = true;
    if (!selectedFarmerId) { setTickets([]); return; }
    sbFetch('grain_delivery_tickets', { filters: [`farmer_id=in.(${grainOwnerKey})`], order: 'delivery_date.desc' })
      .then(r => { if (alive) setTickets(r || []); }).catch(() => { if (alive) setTickets([]); });
    return () => { alive = false; };
  }, [grainOwnerKey, selectedFarmerId]);

  // ── WHAT THE GROWER REPORTED TO CROP INSURANCE ──────────────────────────────
  // The AIP's own production report (P15/P15A -> production_history) is the one
  // authoritative total production figure that exists without asking the grower.
  // Delivered bushels are NOT that number: Kevin, 2026-08-16 — "not everyone
  // delivers bushels to howletts or delivers all at harvest." Sullivan's 2026
  // wheat reports 36,200 bu against 34,204 delivered to Howlett, and the ~2,000
  // difference is real grain that one elevator's ledger cannot see.
  //
  // SUM WITHIN ONE POLICY, NEVER ACROSS THEM. One production year appears in the
  // APH database of every policy year that uses it, so the SAME line is present
  // under -26 and -27 with identical figures — 1,171 cells repeat book-wide and
  // summing them double-counts. Within a single policy the repeats are REAL:
  // 248 cells hold two rows because a county can hold several APH databases
  // (one per FSN/yield line), and those must be added. So: newest policy wins,
  // then sum everything it carries. No dedupe heuristic, no coin flip.
  //
  // BU only. `unit_of_measure` is 'BU' on dry-bean rows that are plainly pounds
  // (2,433 "bu"/ac) and TON on corn silage; this feeds bushel marketing, so a
  // crop is only offered when its rows agree with the crop's own unit.
  const [reportedProd, setReportedProd] = useState({});   // CROP -> {bu, acres, lines, policy}
  useEffect(() => {
    let alive = true;
    if (!selectedFarmerId) { setReportedProd({}); return; }
    sbFetch('production_history', {
      select: REPORTED_PRODUCTION_SELECT,
      filters: [`farmer_id=in.(${grainOwnerKey})`, `crop_year=eq.${cropYear}`],
    }).then(rows => {
      if (!alive) return;
      // The rule lives in src/reportedProduction.js - newest policy wins, then sum
      // everything it carries. Verified identical to the inline version it replaced
      // over 12,217 live rows / 3,969 populated cells, 0 mismatches.
      //
      // grainReportedByMarketingCrop narrows the ROWS to the marketing crop's own
      // practice first and then hands them to that same rule, so ORGANIC CORN's
      // harvested bushels reach the organic row instead of the conventional one.
      // A farm with no organic coverage is offered only base crops, every row
      // reads conventional, and the answer is byte-identical to before.
      setReportedProd(grainReportedByMarketingCrop(rows, marketingCrops, reportedProductionForCrop));
    }).catch(() => { if (alive) setReportedProd({}); });
    return () => { alive = false; };
  }, [grainOwnerKey, selectedFarmerId, cropYear, marketingCropsKey]);
  const reloadTickets = () => grainOwnerKey && sbFetch('grain_delivery_tickets', { filters: [`farmer_id=in.(${grainOwnerKey})`], order: 'delivery_date.desc' }).then(r => setTickets(r || [])).catch(() => {});
  // Parse + upsert a Bushel delivery-ticket CSV/XLSX for the selected farm.
  async function onImportTickets(file) {
    if (!file || !selectedFarmerId) return;
    setTicketImport({ status: 'busy', msg: 'Reading…' });
    try {
      if (!window.XLSX) await new Promise((res, rej) => { const s = document.createElement('script'); s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js'; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
      const buf = await file.arrayBuffer();
      const wb = window.XLSX.read(buf, { cellDates: false, raw: false });
      const rows = window.XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '', raw: false });
      const parsed = parseGrainTicketRows(rows, selectedFarmerId);
      if (!parsed.length) { setTicketImport({ status: 'err', msg: 'No valid tickets found in the file.' }); return; }
      // Which ticket_ids are already on file? (fresh from DB, so the new-vs-dupe count
      // is accurate even if the in-memory list is stale.) Dedup is on ticket_id — the
      // upsert is idempotent, so re-uploading the same export only brings in new loads.
      const existingRows = await sbFetch('grain_delivery_tickets', { filters: [`farmer_id=in.(${grainOwnerKey})`], select: 'ticket_id' }).catch(() => []);
      const existing = new Set((existingRows || []).map(r => String(r.ticket_id)));
      const fresh = parsed.filter(p => !existing.has(String(p.ticket_id)));
      await grainImportTickets(parsed);   // upsert all — constraint dedups, and any re-weighed loads correct
      await reloadTickets();
      const dupes = parsed.length - fresh.length;
      const newBu = fresh.reduce((s, t) => s + (t.net_bushels || 0), 0);
      setTicketImport({ status: 'ok', msg: fresh.length
        ? `${fresh.length} new load${fresh.length > 1 ? 's' : ''} · ${Math.round(newBu).toLocaleString()} bu${dupes ? ` · ${dupes} already on file` : ''}`
        : `No new loads — all ${parsed.length} already on file` });
    } catch (e) { setTicketImport({ status: 'err', msg: e.message || 'Import failed' }); }
  }
  const deliveredBu = (crop) => tickets.filter(t => t.crop === crop && t.crop_year === cropYear).reduce((s, t) => s + (Number(t.net_bushels) || 0), 0);

  // ── MULTI-CROP-YEAR positions overview ──
  // A farm typically has several open positions at once (e.g. 2025 corn tail, 2026
  // corn forward, 2026 wheat delivering, 2027 wheat forward, 2026 soybeans). The
  // detail view is one crop×year; this loads ALL years' contracts + coverage so the
  // overview can show the whole book. Tickets are already all-years.
  const [allContracts, setAllContracts] = useState([]);
  const [covByYear, setCovByYear] = useState({});   // { year: { Wheat|Corn|Soybeans: {guaranteeBu, expectedBu} } }
  useEffect(() => {
    let alive = true;
    if (!selectedFarmerId) { setAllContracts([]); setCovByYear({}); return; }
    (async () => {
      const ids = grainOwnerIds;
      const ac = await sbFetch('grain_contracts', { filters: [`farmer_id=in.(${ids.join(',')})`] }).catch(() => []);
      const pols = await sbFetch('policies', { select: 'id,crop_year', filters: [`farmer_id=in.(${ids.join(',')})`] }).catch(() => []);
      if (!alive) return;
      const yById = {}; pols.forEach(p => { yById[p.id] = p.crop_year; });
      const pids = pols.map(p => p.id);
      let cc = [];
      if (pids.length) cc = await sbFetch('crop_coverage', { select: 'policy_id,crop,plan,aph_yield,coverage_level,acres', filters: [`policy_id=in.(${pids.join(',')})`] }).catch(() => []);
      if (!alive) return;
      const byYear = {};
      cc.forEach(r => { const y = yById[r.policy_id]; if (y == null) return; (byYear[y] = byYear[y] || []).push(r); });
      const cov = {};
      Object.keys(byYear).forEach(y => { cov[y] = { Wheat: grainBushelsFromCoverage(byYear[y], 'Wheat'), Corn: grainBushelsFromCoverage(byYear[y], 'Corn'), Soybeans: grainBushelsFromCoverage(byYear[y], 'Soybeans') }; });
      setAllContracts(ac || []); setCovByYear(cov);
    })();
    return () => { alive = false; };
    // `contracts` in the deps so the all-years position data reloads after any
    // contract mutation (import / add / edit / delete all replace that array) —
    // otherwise the Marketing Plan positions read stale data and show 0% priced
    // even though the Tool table and year-pill counts already updated.
  }, [selectedFarmerId, selectedFarmer, contracts]);
  const contractPriced = (c) => {
    const t = cType(c);
    if (t === 'Fixed Price') return cFutures(c) > 0 || (c.cash_price != null && Number(c.cash_price) > 0);
    if (t === 'HTA') return cFutures(c) > 0;
    if (t === 'Basis') return c.cash_price != null && Number(c.cash_price) > 0;
    return false;
  };
  const positions = useMemo(() => {
    if (!selectedFarmer) return [];
    const out = [];
    marketingCrops.forEach(crop => {
      // Which crop years are relevant NOW — CALENDAR-DRIVEN + data. A year shows if it
      // has data (contracts/coverage/tickets) OR its harvest is within the forward
      // window (≤ ~15 mo out → the new crop you can already forward-price, e.g. 2027
      // wheat this July). Old years without data don't clutter it. Rolls with the date.
      const yset = new Set();
      (allContracts || []).forEach(c => { if (c.crop === crop && c.crop_year) yset.add(+c.crop_year); });
      (tickets || []).forEach(t => { if (t.crop === crop && t.crop_year) yset.add(+t.crop_year); });
      Object.keys(covByYear).forEach(y => { const cov = covByYear[y] && covByYear[y][crop]; if (cov && (cov.guaranteeBu || cov.expectedBu)) yset.add(+y); });
      for (let y = CURRENT_CROP_YEAR - 1; y <= CURRENT_CROP_YEAR + 2; y++) {
        const mth = grainMonthsToHarvest(crop, y);
        if (mth != null && mth >= 0 && mth <= 15) yset.add(y);   // forward-priceable new crop
      }
      [...yset].sort((a, b) => a - b).forEach(year => {
        // Closed old crop years (more than one year back) drop out of the ACTIVE plan —
        // they're finished business, not something to market. Still reachable via the
        // year-pill selector; this only trims the "positions by crop year" cards.
        if (year < CURRENT_CROP_YEAR - 1) return;
        const cs = (allContracts || []).filter(c => c.crop === crop && +c.crop_year === year);
        const ts = (tickets || []).filter(t => t.crop === crop && t.crop_year === year);
        const cov = covByYear[year] ? covByYear[year][crop] : null;
        const guaranteeBu = (cov && cov.guaranteeBu) || (year === CURRENT_CROP_YEAR ? cropGuaranteeBu(crop) : null);
        const expectedBu = (cov && cov.expectedBu) || (year === CURRENT_CROP_YEAR ? expectedBushels(crop) : null);
        const contractedBu = cs.reduce((s, c) => s + cBushels(c), 0);
        const pricedBu = cs.filter(contractPriced).reduce((s, c) => s + cBushels(c), 0);
        const deliveredBu2 = ts.reduce((s, t) => s + (Number(t.net_bushels) || 0), 0);
        const mth = grainMonthsToHarvest(crop, year);
        const hasData = !!(guaranteeBu || contractedBu || deliveredBu2);
        const forward = mth != null && mth >= 0 && mth <= 15;
        if (!hasData && !forward) return;                 // skip empty, non-forward years
        const basisBu = guaranteeBu || expectedBu || contractedBu || deliveredBu2 || null;
        // Un-clamped — a position committed past its guarantee has to read past 100.
        const pricedPct = basisBu ? Math.round(pricedBu / basisBu * 100) : (forward ? 0 : null);
        // Committed (not just priced) vs the insured guarantee, pre-harvest only:
        // this is the delivery obligation the crop still has to cover.
        const overGuarBu = (guaranteeBu > 0 && mth != null && mth > 0) ? Math.max(0, contractedBu - guaranteeBu) : 0;
        const smRow = (planRows || []).find(r => r.crop === crop && +r.crop_year === year);
        const sm = smRow ? (smRow.storage_mode || null) : null;   // deliver-at-harvest → pace to 100% by harvest
        out.push({ crop, year, guaranteeBu, expectedBu, basisBu, contractedBu, pricedBu, deliveredBu: deliveredBu2,
          openStorageBu: Math.max(0, deliveredBu2 - contractedBu), pricedPct, forward: !hasData && forward, overGuarBu,
          paceTarget: grainSchedTarget(crop, year, sm), mth, ticketCount: ts.length });
      });
    });
    return out;
  }, [selectedFarmer, allContracts, tickets, covByYear, planRows]);   // guarantee/expected fns read current-year farmer coverage; planRows → storage mode
  // Keep the selected crop-year VALID for the chosen crop: when the crop changes (or
  // positions load), snap to that crop's current-year position (else its first).
  useEffect(() => {
    const cp = positions.filter(p => p.crop === planCrop);
    if (!cp.length) return;
    if (cp.some(p => p.year === cropYear)) return;
    const p0 = cp.find(p => p.year === CURRENT_CROP_YEAR) || cp[0];
    setCropYear(p0.year);
  }, [planCrop, positions]);

  // Allocate each crop's DELIVERED bushels across its contracts, FIFO by delivery-start
  // date (tickets carry no contract id, so deliveries fill the earliest-window contracts
  // first; leftover = open storage). Live-computed → out[contract.id]=bu, out['open_'+crop]=bu.
  const contractDelivery = useMemo(() => {
    const out = {};
    marketingCrops.forEach(crop => {
      let remaining = tickets.filter(t => t.crop === crop && t.crop_year === cropYear).reduce((s, t) => s + (Number(t.net_bushels) || 0), 0);
      const cs = cropContracts(crop).sort((a, b) => String(a.delivery_start || a.dstart || '') < String(b.delivery_start || b.dstart || '') ? -1 : 1);
      for (const c of cs) { const cap = cBushels(c); const alloc = Math.min(remaining, cap); out[c.id] = alloc; remaining = Math.max(0, remaining - alloc); }
      out['open_' + crop] = remaining;
    });
    return out;
  }, [contracts, tickets, cropYear]);

  // Per-farm marketing data for the Marketing Plan tab: insured-guarantee bushels
  // + booked (priced) bushels per crop. Guarantee is crop-year-scoped (current year
  // from the loaded coverage; other years from futureCovGuar → blank until keyed).
  const yearGuar = (crop) => cropYear === CURRENT_CROP_YEAR ? cropGuaranteeBu(crop) : (futureCovGuar[crop]?.guaranteeBu ?? null);
  const yearExp  = (crop) => cropYear === CURRENT_CROP_YEAR ? expectedBushels(crop) : (futureCovGuar[crop]?.expectedBu ?? null);
  // Storage mode ('bins'|'deliver') + actual harvested production (once known) live
  // on grain_crop_plan. Save is portal-safe (per-farmer write RLS) + optimistic.
  const saveCropField = (crop, patch) => {
    grainSavePlan(selectedFarmerId, cropYear, crop, patch).catch(() => {});
    setPlanRows(prev => {
      const i = prev.findIndex(r => r.crop === crop);
      if (i >= 0) { const next = [...prev]; next[i] = { ...next[i], ...patch }; return next; }
      return [...prev, { farmer_id: selectedFarmerId, crop_year: cropYear, crop, ...patch }];
    });
  };
  const cropData = (crop) => {
    const pr = planRows.find(r => r.crop === crop) || {};
    const contracted = totalCommitted(crop);
    const delivered = deliveredBu(crop);
    return {
      guaranteeBu: yearGuar(crop),
      expectedBu: yearExp(crop),
      pricedBu: pricedBushels(crop),
      storageMode: pr.storage_mode || null,
      actualProduction: pr.actual_production != null ? Number(pr.actual_production) : null,
      acres: Number(getPlan(crop).acres) || null,
      deliveredBu: delivered,
      contractedBu: contracted,
      openStorageBu: Math.max(0, delivered - contracted),   // delivered beyond contracts = open storage
      ticketCount: tickets.filter(t => t.crop === crop && t.crop_year === cropYear).length,
    };
  };
  const farmMarketing = selectedFarmer ? {
    name: selectedFarmer.name,
    // Target alerts link back to the farm so the AGENT copy of the email can name
    // whose target fired (sendAlertEmails looks the name up off farmer_id).
    farmerId: selectedFarmerId,
    byCrop: { Wheat: cropData('Wheat'), Corn: cropData('Corn'), Soybeans: cropData('Soybeans') },
  } : null;
  // Deduped farm options (one per name — selectedFarmer merges same-name cards).
  const farmOptions = Object.values(
    farmerList.reduce((m, f) => { const k = (f.name || '').trim().toLowerCase(); if (k && !m[k]) m[k] = f; return m; }, {})
  );
  // Portal / single-operation case: if there's exactly one farm, select it so the
  // user lands straight on their grain view (no picking their only farm). Admins
  // see the whole book, so this only fires for a one-farm portal login.
  useEffect(() => {
    if (!selectedFarmerId && farmOptions.length === 1) setSelectedFarmerId(farmOptions[0].id);
  }, [farmOptions.length, selectedFarmerId]);

  // Landing view — tabs are ALWAYS visible. The full per-farm dashboard renders
  // only once a farm is chosen inside the Marketing Tool tab (falls through
  // below). Cash Board (default) / Markets are global; the Marketing Tool tab
  // shows the farm picker until a farm is selected.
  // ⚠ THE TABS WRAP, AND NOTHING GOES OFF THE EDGE. Measured on production
  // 2026-09-24 at a 390px viewport: five tabs at `padding: 9px 20px` put
  // "📈 Markets" at 430px and "🏬 Balances" at 542px, and with no scroller of its
  // own the overflow reached the main content area — so the whole Grain page
  // dragged sideways and the heading slid off to the left.
  //
  // WRAP RATHER THAN SCROLL, and every tab strip in this app now makes the same
  // choice. Giving the strip its own overflow-x:auto also keeps the page still —
  // but it hides the last tabs behind a swipe with nothing on screen to say they
  // are there, which is how the Claims "Cross-check" tab became invisible on a
  // phone. A second row costs nothing; an unreachable tab costs the feature.
  // The only page with no title — it opened straight onto its tabs. Same title
  // style as every other page.
  const topTabBar = (<>
    {/* Inside a farm the dark header already reads GRAIN MARKETING over the farm
        name; a second title under the tickers said it twice. */}
    {!selectedFarmerId && <h2 style={{ margin: '0 0 10px', color: T.ink, fontSize: isMobile ? 21 : 25, fontWeight: 800, fontFamily: T.serif }}>Grain Marketing</h2>}
    {/* The tabs WRAP, deliberately (check-fsa426-pdf): a sideways-scrolling strip
        hides a tab off-screen. On a phone they are just tighter, so five tabs
        take two rows instead of three. */}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14,
                  borderBottom: '2px solid #e4ece4', paddingBottom: 0 }}>
      {[...(liveBidsOk ? [['livebids', '⚡ Live Bids']] : []), ...(cashBoardVisible ? [['cashboard', cashBoardOn === false ? '🌾 Cash Board (off)' : '🌾 Cash Board']] : []), ['tool', '🧰 Marketing Tool'], ['plan', '🎯 Targets'], ['markets', '📈 Markets']].map(([key, label]) => (
        <button key={key} onClick={() => { if (key === 'cashboard') setCbPicked(true); setTopTab(key); }}
          style={{ padding: isMobile ? '8px 12px' : '9px 20px', border: 'none', borderBottom: `3px solid ${effTopTab === key ? '#2d4a2d' : 'transparent'}`,
            background: 'none', cursor: 'pointer', fontFamily: 'inherit', marginBottom: -2, whiteSpace: 'nowrap',
            fontSize: isMobile ? 13.5 : 15, fontWeight: effTopTab === key ? 800 : 600, color: effTopTab === key ? '#1a2e1a' : '#60735f' }}>
          {label}
        </button>
      ))}
    </div>
  </>
  );

  // The Marketing Tool is the only farm-scoped tab — with a farm selected it renders in
  // the full shell below (dark header w/ year badges + futures chips). Cash Board,
  // Targets, and Markets are all farm-agnostic and render in the simpler branch here.
  // Targets is the generic GrainTargetPlanner: the price percentile + resistance
  // levels, book-wide, no farm required. It is the ONLY copy — Market
  // Intelligence carried a second Targets tab of the same screen until 2026-11-02.
  if (!(effTopTab === 'tool' && selectedFarmerId)) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', fontFamily: "'Plus Jakarta Sans','Helvetica Neue',sans-serif" }}>
        {error && (
          <div style={{ background: '#fdecea', border: '1px solid #e0a0a0', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 12, color: '#c0392b' }}>
            {error} <button onClick={() => setError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', fontWeight: 800 }}>✕</button>
          </div>
        )}
        {topTabBar}
        {effTopTab === 'cashboard' && cashBoardVisible && <GrainCashBoard liveFutures={liveFutures} isMobile={isMobile} portal={isPortal}
          adminSwitch={cbAdmin ? { on: cashBoardOn, saving: cashBoardSaving, onToggle: toggleCashBoard } : null} />}
        {effTopTab === 'livebids' && liveBidsOk && <LiveBidBoard liveFutures={liveFutures} isMobile={isMobile} />}
        {effTopTab === 'plan' && <GrainTargetPlanner generic isMobile={isMobile} />}
        {effTopTab === 'markets' && <GrainMarketsPanel isMobile={isMobile} />}
        {effTopTab === 'tool' && (
          <GrainFarmerSelector
            farmerList={farmerList}
            cropYear={cropYear}
            isMobile={isMobile}
            onSelect={(f) => setSelectedFarmerId(f.id)}
            cardStyle={S.card}
          />
        )}
      </div>
    );
  }

  const totalSoldBu   = marketingCrops.reduce((s, c) => s + soldBu(c), 0);
  const totalUnsoldBu = marketingCrops.reduce((s, c) => s + (unsoldBu(c) || 0), 0);
  const totalRevenue  = marketingCrops.reduce((s, c) => s + cropRevenue(c), 0);
  const totalExpenses = marketingCrops.reduce((s, c) => s + cropExpenses(c), 0);
  const totalGP       = totalRevenue - totalExpenses;
  const totalAcres    = marketingCrops.reduce((s, c) => s + getPlan(c).acres, 0);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', fontFamily: "'Plus Jakarta Sans','Helvetica Neue',sans-serif" }}>
      <div style={{ background: '#1a2e1a', borderRadius: 12, padding: isMobile ? '18px 18px' : '22px 28px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', color: '#4a7a4a', textTransform: 'uppercase', marginBottom: 5 }}>Grain Marketing</div>
          <h2 style={{ margin: '0 0 6px', fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#fff', fontFamily: "'Lora',serif" }}>{selectedFarmer?.name}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {(() => {
              // Show all years that have contracts on file (so the user
              // can navigate to historical data without guessing), plus
              // current and next crop year as defaults.
              const yearsFromData = Object.keys(yearCounts).map(Number).filter(Boolean);
              const defaults = [cropYear - 1, cropYear, cropYear + 1];
              const all = Array.from(new Set([...yearsFromData, ...defaults])).sort((a, b) => a - b);
              return all.map(yr => {
                const count = yearCounts[yr] || 0;
                return (
                  <button key={yr} onClick={() => { setCropYear(yr); setCropFilter('All'); }}
                    style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '1px solid', display: 'flex', alignItems: 'center', gap: 6,
                      background: cropYear === yr ? '#4a7a4a' : 'transparent', color: cropYear === yr ? '#fff' : '#4a7a4a', borderColor: cropYear === yr ? '#4a7a4a' : '#2d4a2d' }}>
                    {yr}
                    {count > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10,
                        background: cropYear === yr ? 'rgba(255,255,255,0.2)' : '#1a2e1a', color: cropYear === yr ? '#fff' : '#7aaa7a' }}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              });
            })()}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {isCurrentUserAdmin() && (
            <button onClick={() => openFuturesModal()} style={{ ...S.btnOut, fontSize: 12, padding: '6px 12px' }}>Edit Futures</button>
          )}
          {/* Secondary entry point only — the prominent one lives on the Contracts
              tab. Kept so a contract can still be added from any tab without
              switching first. */}
          <button onClick={() => openNewContract()}
            style={{ ...S.btnGreen, fontSize: 12, padding: '6px 14px' }}>+ Add Contract</button>
          {farmOptions.length > 1 && <button onClick={() => setSelectedFarmerId(null)} style={{ ...S.btnSmOut }}>← Farms</button>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {marketingCrops.map(crop => {
          const f = getFutures(crop);
          return (
            <div key={crop} onClick={() => openFuturesModal()} style={{ background: '#fff', border: '1px solid #dde8d8', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: grainCropColor(crop), textTransform: 'uppercase', letterSpacing: '0.06em' }}>{grainBaseCrop(crop) || crop}</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#1a2e1a', fontVariantNumeric: "tabular-nums" }}>${Number(f.price).toFixed(2)}</span>
              <span style={{ fontSize: 11, color: '#60735f' }}>{f.month}</span>
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ background: '#fdecea', border: '1px solid #e0a0a0', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 12, color: '#c0392b' }}>
          {error} <button onClick={() => setError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', fontWeight: 800 }}>✕</button>
        </div>
      )}

      {topTabBar}

      {effTopTab === 'tool' && (
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          // Admin/shadow only (Phase IV) — the button itself is the first gate,
          // the render block below is the second (CLAUDE.md "hide the surface,
          // then guard the fetch, and gate at the render").
          ['dashboard', '📊 Dashboard'], ['contracts', '📋 Contracts'], ['tickets', '🎫 Scale Tickets'], ['plan', '🌱 Crop Plan'], ['cop', '🧮 Cost of Production'], ['import', '⬆ Import'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: tab === key ? 800 : 600, cursor: 'pointer', border: '1px solid',
              background: tab === key ? '#2d4a2d' : '#fff', color: tab === key ? '#fff' : '#4a6e4a', borderColor: tab === key ? '#2d4a2d' : '#c8d8c0', fontFamily: 'inherit' }}>
            {label}
          </button>
        ))}
      </div>
      )}

      {effTopTab === 'tool' && loading && <div style={{ textAlign: 'center', padding: '60px 0', color: '#60735f', fontSize: 14 }}>Loading grain data…</div>}

      {effTopTab === 'tool' && !loading && <>
        {tab === 'dashboard' && <>
          {/* Cash flow by delivery month — moved here from the Summary
              2026-08-28. It is a detailed money-over-time view, which is a
              Dashboard question; the Summary answers "what needs a decision".
              Unchanged otherwise: the same buildMonthlyCashFlow() and the same
              three-state stacking (priced / basis-open estimate / bushels with
              no price), which tools/check-grain-cashflow.js still guards. An
              HTA is valued at the SAME observed basis the Summary's own cash
              resolver selected, never a house assumption; with no observed
              basis those bushels render as committed-without-a-price. */}
          {isCurrentUserAdmin() && !isPortal && actionResult && (() => {
            const bRef = actionResult.action?.cash?.basis;
            const hasRef = typeof bRef === 'number' && isFinite(bRef);
            const flow = buildMonthlyCashFlow(actionResult.contracts || [], {
              basisRef: hasRef ? bRef : null,
              basisRefLabel: hasRef ? (actionResult.action?.cash?.sourceLabel || null) : null,
            });
            return (
              <div style={{ ...S.card, marginBottom: 16 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#60735f', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                  {actionCrop} {cropYear} — cash flow by delivery month
                </div>
                <GrainCashFlowChart flow={flow} isMobile={isMobile} />
              </div>
            );
          })()}
          {/* Hero strip — bushels sold per crop, the headline number a farmer wants
              to see first. Each card: total contracted bushels for that crop +
              count of contracts + avg cash price (priced bushels only). */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${marketingCrops.length}, 1fr)`, gap: 10, marginBottom: 16 }}>
            {marketingCrops.map(crop => {
              const sold = soldBu(crop);
              // Shadowing the view-level selector with its own result made this a
              // const initialised from itself — "Cannot access before initialization",
              // and the whole page rendered as the error boundary.
              const thisCropContracts = cropContracts(crop);
              const avgSold = avgCashSold(crop);
              return (
                <div key={crop} style={{ background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, padding: '14px 18px', borderLeft: `4px solid ${grainCropColor(crop)}` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: grainCropColor(crop), textTransform: 'uppercase', marginBottom: 4 }}>{crop} sold</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#1a2e1a', fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
                    {sold > 0 ? Math.round(sold).toLocaleString() : '—'}
                    {sold > 0 && <span style={{ fontSize: 13, color: '#60735f', fontWeight: 600, marginLeft: 4 }}>bu</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#60735f', marginTop: 4, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span>{thisCropContracts.length} contract{thisCropContracts.length === 1 ? '' : 's'}</span>
                    {avgSold != null && <span>avg <strong style={{ color: '#2d5a10', fontVariantNumeric: "tabular-nums" }}>{gFmtD(avgSold)}</strong></span>}
                  </div>
                </div>
              );
            })}
          </div>

          {contracts.length === 0 && (
            <div style={{ ...S.card, textAlign: 'center', padding: '40px 24px', border: '2px dashed #c8d8c0' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌾</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2e1a', marginBottom: 4 }}>No contracts on file for {cropYear}</div>
              <div style={{ fontSize: 12, color: '#60735f', marginBottom: 14 }}>
                {Object.keys(yearCounts).length > 0
                  ? `Other years have data — try ${Object.keys(yearCounts).map(Number).sort((a,b)=>b-a).slice(0,3).join(', ')}`
                  : 'Add a contract above or import from a spreadsheet.'}
              </div>
              <button onClick={() => openNewContract()} style={S.btnGreen}>+ Add Contract</button>
            </div>
          )}

          {/* ORGANIC ACRES, AS THEIR OWN POSITION.

              A farmer asked for this: organic is not the same crop. RMA rates
              and PRICES it separately - CY2026 wheat is $5.76 conventional
              against $11.16 organic - so an acre-weighted blend produces an APH
              true of neither field. Palladino and Carley's CY2026 Onondaga corn
              is 383.5 organic acres at 87.4 bu beside 228.3 conventional at
              149.4; blended that reads 110.5, which nobody farms.

              ⚠ AND THE MARKETING HALF SPLITS TOO, WHICH IS A TRADE KEVIN MADE
              DELIBERATELY. Not one contract in this book states a practice, so
              the first cut left an unmarked contract on a dual-practice farm
              UNASSIGNED - the cautious reading, and it cost R L JEFFRES all 28
              of his contracts (477,037 bu): every card read 0 contracts and 0%
              committed while the bushels sat in a callout. A ceiling saying
              "you have sold nothing" is not more careful than one saying "you
              have sold this much conventional"; it is wrong in the direction
              that invites overselling. Kevin, 2026-09-05: "recover those and
              put those under conventional corn and wheat."

              THE COST IS REAL AND IS NAMED ON SCREEN. Measured on W CLEVELAND
              FARMS, whose CY2026 corn is 275.5 organic acres against 239.1
              conventional: his three unmarked corn contracts (15,000 bu) now
              measure against the CONVENTIONAL guarantee alone, reading 37% of
              expected and 49% of guarantee. If any of those bushels are in
              fact organic, that number is high. So the callout below states
              how many contracts are there by default and on which crops - the
              only thing standing between a defensible default and an invented
              fact is the page saying which it is.

              It renders only where the farm HAS insured organic acres. */}
          {/* ⚠ A DEFAULT IS DECLARED, NEVER SILENT. These contracts are counted
              and marketed as conventional because nothing says otherwise, and a
              reader has no other way to tell that from a practice somebody
              chose. Empty on every farm insuring one practice per commodity,
              which is all but ten of them. */}
          {(() => { const defaultedByCrop = defaultedGroups();
            const defaultedBu = defaultedByCrop.reduce((t, x) => t + x.rows.reduce((a, c) => a + (cBushels(c) || 0), 0), 0);
            return defaultedByCrop.length > 0 && (
            <div style={{ ...S.card, marginBottom: 16, borderLeft: "4px solid #b8860b" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: "#8a5a00", textTransform: "uppercase", marginBottom: 4 }}>
                Counted as conventional by default
              </div>
              <div style={{ fontSize: 11.5, color: "#60735f" }}>
                {gFmtBu(defaultedBu)} — {defaultedByCrop.map((x) => `${x.base} ${x.rows.length}`).join(" · ")}.
                {" "}This farm insures both practices for {defaultedByCrop.length === 1 ? "that crop" : "those crops"} and the contracts do not say which fills them.
                {" "}Set <b>Practice</b> on a contract to move it to organic.
              </div>
            </div>
          ); })()}
          {(() => { const unassignedByCrop = unassignedGroups();
            const unassignedBu = unassignedByCrop.reduce((t, x) => t + x.rows.reduce((a, c) => a + (cBushels(c) || 0), 0), 0);
            return unassignedByCrop.length > 0 && (
            <div style={{ ...S.card, marginBottom: 16, borderLeft: "4px solid #a5461b" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: "#a5461b", textTransform: "uppercase", marginBottom: 4 }}>
                Needs a practice
              </div>
              <div style={{ fontSize: 11.5, color: "#60735f" }}>
                {gFmtBu(unassignedBu)} — {unassignedByCrop.map((x) => `${x.base} ${x.rows.length}`).join(" · ")}.
                {" "}These were written after the practice field existed and still do not say which
                {" "}side they fill, so they are counted on <b>neither</b> card. Set <b>Practice</b> on each.
              </div>
            </div>
          ); })()}
          {hasOrganic && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 4 }}>Insured position by practice</div>
              <div style={{ fontSize: 11, color: '#60735f', marginBottom: 12 }}>
                Organic acres are rated and priced separately by RMA, so they carry their own position and their own column above. A contract is placed by its practice: where the farm insures only one for a crop that is read from the coverage, and where it insures both an unmarked contract counts as conventional — reported above, so a default never passes as a statement.
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    {['Position', 'Adjusted ac', 'APH', 'Coverage', 'Guarantee (bu)', 'Reported (bu)'].map((h, i) => (
                      <th key={h} style={{ ...S.th, textAlign: i ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {marketingCrops.map(mc => {
                      const b = cropInsurance(mc);
                      const rp = reportedProd[mc];
                      if (!b || !(b.acres > 0)) return null;
                      return (
                        <tr key={mc}>
                          <td style={{ ...S.td, fontWeight: 700, color: grainCropColor(mc) }}>{mc}</td>
                          <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{(() => { const a = b.grainAcres != null ? b.grainAcres : b.acres; return a ? a.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'; })()}</td>
                          <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{b.aph ? b.aph.toFixed(1) : '—'}</td>
                          <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{b.coverage ? Math.round(b.coverage * 100) + '%' : '—'}</td>
                          <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{b.guaranteeBu ? Math.round(b.guaranteeBu).toLocaleString() : '—'}</td>
                          <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: "tabular-nums" }}>{rp && rp.bu ? Math.round(rp.bu).toLocaleString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Financial summary (Revenue/Expenses/Profit) moved below the per-crop cards. */}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(3, marketingCrops.length)}, 1fr)`, gap: 12, marginBottom: 16 }}>
            {marketingCrops.map(crop => {
              const plan = getPlan(crop);
              const expected = totalBu(crop);
              const committed = totalCommitted(crop);
              const priced = pricedBushels(crop);
              const unpriced = unpricedCommittedBushels(crop);
              const open = expected > 0 ? Math.max(0, expected - committed) : null;
              const guarantee = cropGuaranteeBu(crop);
              const byType = committedByType(crop);
              const avgSold = avgCashSold(crop);
              const avgFullSellout = avgCashFullSellout(crop);
              const fwdAvg = fwdAvgPrice(crop);
              const pctExp = pctOfExpected(crop);
              const pctGuar = pctOfGuarantee(crop);
              const overSold = overSoldNote(crop);
              const insInfo = cropInsurance(crop);
              const covGaps = insInfo?.incomplete || [];
              const estBasisBu = estimatedBasisBushels(crop);

              // Donut: segments by contract type, capped at expected if a plan exists
              const segments = GRAIN_CONTRACT_TYPES
                .filter(tt => byType[tt] > 0)
                .map(tt => ({ value: byType[tt], color: GRAIN_TYPE_COLORS[tt], label: tt }));
              const hasPlan = expected > 0;
              const centerLabel = hasPlan
                ? `${Math.round(pctExp || 0)}%`
                : (committed > 0 ? Math.round(committed / 1000) + 'K' : '—');
              const centerSub = hasPlan ? 'of expected' : (committed > 0 ? 'bushels' : 'no plan');

              const fmtBuShort = n => n == null ? '—' : Math.round(n).toLocaleString();

              return (
                <div key={crop} style={S.card}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: grainCropColor(crop), fontFamily: "'Lora',serif" }}>{crop}</span>
                    <span style={{ fontSize: 11, color: '#60735f', fontVariantNumeric: "tabular-nums" }}>{getFutures(crop).month}</span>
                  </div>

                  {/* Pie at TOP */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                    <ContractTypeDonut
                      segments={segments.length ? segments : [{ value: 0.0001, color: '#e0e8e0' }]}
                      cap={hasPlan ? expected : undefined}
                      centerLabel={centerLabel}
                      centerSub={centerSub}
                      size={140}
                    />
                  </div>

                  {/* Per-type legend */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #f0f4f0' }}>
                    {GRAIN_CONTRACT_TYPES.filter(tt => byType[tt] > 0).map(tt => (
                      <div key={tt} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                        <div style={{ width: 9, height: 9, borderRadius: 2, background: GRAIN_TYPE_COLORS[tt], flexShrink: 0 }} />
                        <span style={{ flex: 1, color: '#1a2e1a', fontWeight: 600 }}>{tt}</span>
                        <span style={{ fontVariantNumeric: "tabular-nums", color: '#60735f' }}>{fmtBuShort(byType[tt])} bu</span>
                      </div>
                    ))}
                    {Object.keys(byType).length === 0 && (
                      <div style={{ fontSize: 11, color: '#a8b8a8', fontStyle: 'italic', textAlign: 'center' }}>No contracts on file</div>
                    )}
                  </div>

                  {/* Bushels summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 12 }}>
                    <span style={{ color: '#60735f' }}>Expected</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", textAlign: 'right', fontWeight: 700, color: '#1a2e1a' }}>{expected > 0 ? fmtBuShort(expected) : '—'}</span>
                    <span style={{ color: '#60735f' }}>Committed</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", textAlign: 'right', fontWeight: 700, color: '#1a2e1a' }}>{fmtBuShort(committed)}</span>
                    <span style={{ color: '#60735f' }}>Priced</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", textAlign: 'right', color: '#2d5a10' }}>{fmtBuShort(priced)}</span>
                    <span style={{ color: '#60735f' }}>Unpriced (committed)</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", textAlign: 'right', color: unpriced > 0 ? '#7a4a00' : '#a8b8a8' }}>{fmtBuShort(unpriced)}</span>
                    {open != null && (
                      <>
                        <span style={{ color: '#60735f' }}>Open (uncontracted)</span>
                        <span style={{ fontVariantNumeric: "tabular-nums", textAlign: 'right', color: open > 0 ? '#c0392b' : '#a8b8a8' }}>{fmtBuShort(open)}</span>
                      </>
                    )}
                  </div>

                  {/* Delivery progress — scale tickets delivered vs contracted bushels */}
                  {(() => {
                    const del = deliveredBu(crop);
                    const contracted = cropContracts(crop).reduce((s, c) => s + cBushels(c), 0);
                    const openStor = contractDelivery['open_' + crop] || 0;
                    if (del <= 0 && contracted <= 0) return null;
                    const pct = contracted > 0 ? Math.round(del / contracted * 100) : (del > 0 ? 100 : 0);
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#60735f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Delivered (scale tickets)</span>
                          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 11, color: '#1a2e1a', fontWeight: 700 }}>{del > 0 ? Math.round(del).toLocaleString() : '—'}{contracted > 0 ? <span style={{ color: '#677167', fontWeight: 400 }}> / {Math.round(contracted).toLocaleString()}</span> : ''}</span>
                        </div>
                        {contracted > 0 && <GrainProgressBar pct={pct} height={7} color="#2d6a2d" over={openStor > 0} />}
                        {openStor > 0 && <div style={{ fontSize: 10.5, color: '#a06a00', fontWeight: 700, marginTop: 3, textAlign: 'right' }}>+{Math.round(openStor).toLocaleString()} bu open storage</div>}
                      </div>
                    );
                  })()}

                  {/* Pricing summary */}
                  <div style={{ background: '#f9fcf6', borderRadius: 7, padding: '8px 11px', marginBottom: 12, display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 12px', fontSize: 11 }}>
                    <span style={{ color: '#4a6e4a', fontWeight: 600 }}>Avg cash (sold so far){estBasisBu > 0 ? <span title={`${Math.round(estBasisBu).toLocaleString()} bu are HTAs with basis not yet fixed — their cash leg is filled from today's forward curve and moves with the board.`} style={{ color: '#b8860b', fontWeight: 800 }}> *</span> : null}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", textAlign: 'right', fontWeight: 800, color: '#2d5a10' }}>{avgSold != null ? gFmtD(avgSold) : '—'}</span>
                    {estBasisBu > 0 && (
                      <span style={{ gridColumn: '1 / -1', fontSize: 10.5, color: '#8a7a4a', lineHeight: 1.4, marginTop: 2 }}>
                        * includes {Math.round(estBasisBu).toLocaleString()} bu of HTA with basis <b>not yet fixed</b> — priced off today's forward curve, so this figure moves with the board.
                      </span>
                    )}
                    <span style={{ color: '#4a6e4a', fontWeight: 600 }} title="If you sold all unpriced + open bushels at the forward curve, your year-average becomes this">Avg if full sellout @ fwd</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", textAlign: 'right', fontWeight: 800, color: '#1a4a7a' }}>{avgFullSellout != null ? gFmtD(avgFullSellout) : '—'}</span>
                    <span style={{ color: '#60735f' }}>Fwd curve avg</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", textAlign: 'right', color: '#60735f' }}>{fwdAvg ? gFmtD(fwdAvg) : '—'}</span>
                  </div>

                  {/* Crop insurance */}
                  <div style={{ background: '#fdf8ec', borderRadius: 7, padding: '8px 11px', marginBottom: 10, fontSize: 11 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#7a4a00', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Crop Insurance{plan.fromInsurance && <span style={{ fontWeight: 600, color: '#2d6a1a', letterSpacing: 0, textTransform: 'none' }}> · live from policy</span>}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                      <div>
                        <label style={{ fontSize: 10, color: '#60735f', display: 'block' }}>APH (bu/ac)</label>
                        {/* 1dp. This is the acre-weighted APH, so its 14-digit tail
                            (75.04199208634584) is arithmetic residue, not accuracy —
                            and the box is read-only when it comes from the policy, so
                            there is nothing to round-trip. */}
                        <input type="number" key={`aph-${selectedFarmerId}-${crop}-${plan.fromInsurance ? 'live' : 'man'}-${plan.aph || ''}`}
                          defaultValue={plan.aph ? Math.round(plan.aph * 10) / 10 : ''} placeholder="180" min="0" step="0.1"
                          disabled={plan.fromInsurance}
                          style={{ ...S.inputSm, width: '100%', ...(plan.fromInsurance ? { background: '#f4efe0', cursor: 'default' } : {}) }}
                          onBlur={e => { if (!plan.fromInsurance) handlePlanChange(crop, 'aph_yield', e.target.value); }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#60735f', display: 'block' }}>Coverage</label>
                        {/* A DISABLED DROPDOWN THAT READ "—" ON A COVERED CROP. Two
                            separate reasons, and neither was the farm's fault:
                              • float residue — the acre-weighted level for Sullivan's
                                wheat is 0.7999999999999999, which string-matches no
                                <option value="0.8">, so the select fell to its blank;
                              • a real BLEND — Meisenzahl-style splits (75% in one
                                county, 80% in another) weight to 0.76, and 76% is not
                                an option at all, so it could never have matched.
                            When the figure comes from the policy this is read-only
                            anyway, so it is now TEXT: the true level, said plainly,
                            and named as a blend when it is one. A picker you cannot
                            use, showing a blank on a crop that is covered, is worse
                            than no control. */}
                        {plan.fromInsurance ? (() => {
                          const pct = plan.coverage > 0 ? plan.coverage * 100 : null;
                          const std = [50, 55, 60, 65, 70, 75, 80, 85, 90, 95];
                          const blended = pct != null && !std.some(s => Math.abs(pct - s) < 0.05);
                          return (
                            <div style={{ ...S.inputSm, width: '100%', background: '#f4efe0', cursor: 'default',
                                          display: 'flex', alignItems: 'center', gap: 5 }}
                                 title={blended ? 'Acre-weighted across counties insured at different levels — the guarantee is still summed per line, never blended' : undefined}>
                              <b>{pct == null ? '—' : `${Math.round(pct * 10) / 10}%`}</b>
                              {blended && <span style={{ fontSize: 9.5, color: '#8a7a4a', fontWeight: 700 }}>blended</span>}
                            </div>
                          );
                        })() : (
                          <select key={`cov-${selectedFarmerId}-${crop}-man-${plan.coverage || ''}`}
                            defaultValue={plan.coverage ? String(Math.round(plan.coverage * 100) / 100) : ''}
                            style={{ ...S.inputSm, width: '100%' }}
                            onChange={e => handlePlanChange(crop, 'coverage_level', e.target.value)}>
                            <option value="">—</option>
                            {[0.50, 0.55, 0.60, 0.65, 0.70, 0.75, 0.80, 0.85, 0.90, 0.95].map(v => (
                              <option key={v} value={v}>{Math.round(v * 100)}%</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#7a4a00', fontWeight: 600 }}>Guaranteed bu</span>
                      <input type="number"
                        key={`guar-${selectedFarmerId}-${crop}-${plan.fromInsurance ? 'live' : 'man'}-${guarantee != null ? Math.round(guarantee) : ''}`}
                        defaultValue={(!plan.fromInsurance && plan.guaranteeOverride != null) ? plan.guaranteeOverride : (guarantee != null ? Math.round(guarantee) : '')}
                        disabled={plan.fromInsurance}
                        title={plan.fromInsurance ? 'Live from crop insurance (insured acres × APH × coverage)' : ''}
                        placeholder={guarantee != null ? Math.round(guarantee).toString() : 'auto'}
                        style={{ ...S.inputSm, width: 90, textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 700, color: '#7a4a00', ...(plan.fromInsurance ? { background: '#f4efe0', cursor: 'default' } : {}) }}
                        onBlur={e => {
                          if (plan.fromInsurance) return; // keyed farmers: live only
                          const v = e.target.value === '' ? null : Number(e.target.value);
                          handlePlanChange(crop, 'insurance_guarantee_override', v);
                        }}
                      />
                    </div>
                  </div>

                  {/* % COMMITTED of expected + guarantee. These count every contracted
                      bushel (Bushels-Only and unfixed HTAs included) because the question
                      here is DELIVERY obligation — what has to come off the field. The
                      Marketing Plan's "% priced" counts only bushels with a locked price:
                      a different question, so both labels say which. Neither is clamped. */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div style={{ background: '#fff', border: `1px solid ${pctExp > 100 ? '#e0b070' : '#c8d8c0'}`, borderRadius: 7, padding: '8px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#60735f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Committed / Expected</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: pctExp == null ? '#a8b8a8' : pctExp > 100 ? '#b8860b' : grainCropColor(crop), fontVariantNumeric: "tabular-nums" }}>
                        {pctExp != null ? `${Math.round(pctExp)}%` : '—'}
                      </div>
                    </div>
                    <div style={{ background: '#fff', border: `1px solid ${pctGuar > 100 ? (overSold?.tone === 'bad' ? '#e09a9a' : '#e0b070') : '#c8d8c0'}`, borderRadius: 7, padding: '8px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#60735f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Committed / Guarantee</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: pctGuar == null ? '#a8b8a8' : pctGuar > 100 ? (overSold?.tone === 'bad' ? '#c0392b' : '#b8860b') : '#7a4a00', fontVariantNumeric: "tabular-nums" }}>
                        {pctGuar != null ? `${Math.round(pctGuar)}%` : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Over the guarantee — say by how many bushels, and whether it matters.
                      Pre-harvest that's live production risk; once the crop is off, the
                      bushels exist and there is no cap. */}
                  {overSold && (
                    <div style={{ marginTop: 8, background: overSold.tone === 'bad' ? '#fdf0ee' : '#fdf8ec', border: `1px solid ${overSold.tone === 'bad' ? '#e09a9a' : '#e6d4a8'}`, borderRadius: 7, padding: '7px 10px', fontSize: 10.5, lineHeight: 1.45, color: overSold.tone === 'bad' ? '#8a2a1a' : '#7a4a00' }}>
                      <b>{overSold.tone === 'bad' ? '⚠ Sold past the guarantee' : 'Over the guarantee'}</b> — {overSold.text}
                    </div>
                  )}

                  {/* ACTUAL PRODUCTION, on the card that asks for it. This note has been
                      saying "enter actual production to confirm" with nowhere on the
                      screen to enter it — the box lived on the Crop Plan tab. It is here
                      now, in TOTAL BUSHELS (the column is bushels; a bu/ac box has to
                      multiply by acres, and a wrong acre figure then corrupts it).
                      Beside it, what the grower already told the AIP: that is the one
                      authoritative total that exists without asking, and it is NOT
                      delivered bushels — Sullivan reports 36,200 bu of wheat against
                      34,204 delivered to Howlett. Offered, never applied on its own. */}
                  {(() => {
                    const yi = yieldInfo(crop);
                    const rep = reportedProd[crop];
                    if (!rep && yi.actualBu == null && !(overSold || yi.acres > 0)) return null;
                    const same = yi.actualBu != null && rep && Math.abs(yi.actualBu - rep.bu) < 1;
                    return (
                      <div style={{ marginTop: 8, background: '#f4f8f0', border: '1px solid #dde8d8', borderRadius: 7, padding: '7px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#4a6e4a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Actual production</span>
                          <input type="number" min="0" step="1"
                            key={`act-${selectedFarmerId}-${crop}-${cropYear}-${yi.actualBu ?? ''}`}
                            defaultValue={yi.actualBu != null ? Math.round(yi.actualBu) : ''}
                            placeholder="total bu"
                            style={{ ...S.inputSm, width: 110, textAlign: 'right', fontVariantNumeric: "tabular-nums", fontWeight: 700 }}
                            onBlur={e => {
                              const b = parseFloat(e.target.value);
                              handlePlanChange(crop, { actual_production: (!isNaN(b) && b > 0) ? Math.round(b) : null });
                            }} />
                          <span style={{ fontSize: 10, color: '#60735f' }}>
                            {yi.actualBu != null && yi.acres > 0 ? `${gFmtY(yi.actualBu / yi.acres)} bu/ac` : 'total bushels'}
                          </span>
                        </div>
                        {rep && (
                          <div style={{ marginTop: 5, fontSize: 10, color: '#4a6e4a', lineHeight: 1.45 }}>
                            <b style={{ color: '#2d4a2d' }}>Reported to crop insurance:</b>{' '}
                            <b style={{ color: '#1a2e1a' }}>{gFmtBu(rep.bu)}</b>
                            {rep.acres ? <span style={{ color: '#60735f' }}> on {gFmtAc(rep.acres)} ac · {gFmtY(rep.bu / rep.acres)} bu/ac</span> : null}
                            {same
                              ? <span style={{ color: '#2d6a1a', fontWeight: 700 }}> · in use</span>
                              : <button onClick={() => handlePlanChange(crop, { actual_production: Math.round(rep.bu) })}
                                  style={{ marginLeft: 6, cursor: 'pointer', font: 'inherit', fontSize: 10, fontWeight: 700,
                                           border: '1px solid #2d4a2d', background: '#2d4a2d', color: '#fff',
                                           borderRadius: 5, padding: '1px 7px' }}>Use this</button>}
                            <div style={{ color: '#677167', marginTop: 2 }}>
                              {rep.lines} yield line{rep.lines === 1 ? '' : 's'} on policy {rep.policy} — the APH database, not delivered bushels.
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* A coverage line that produced no bushels, or one carrying liability
                      with no reported acres, makes the guarantee read SHORT — which makes
                      % committed read HIGH, the same direction as over-selling. Never let
                      that be silent. */}
                  {covGaps.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 10, color: '#8a7a4a', lineHeight: 1.45 }}>
                      ⓘ Guarantee excludes {covGaps.length} coverage line{covGaps.length > 1 ? 's' : ''} ({[...new Set(covGaps.map(g => g.county))].join(', ')}) — {[...new Set(covGaps.map(g => g.why))].join('; ')}. The real guarantee is higher than shown.
                    </div>
                  )}
                  {plan.fromInsurance && insInfo?.acresVia === 'intended' && (
                    <div style={{ marginTop: 6, fontSize: 10, color: '#8a7a4a', lineHeight: 1.45 }}>
                      ⓘ Acres are <b>intended</b> (prevent-plant max eligible) — reported acres aren't keyed for this crop yet.
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Forward Price Curve — interactive scenario editor.
              Adjust % allocation / basis per delivery month (futures are live
              daily settles, not editable) and the per-crop dashboard cards above
              re-compute in real time ("Avg if full sellout @ fwd"). */}
          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: '#4a6e4a', textTransform: 'uppercase' }}>
                  Forward Price Curve
                  {fwdSave?.status === 'saving' && <span style={{ marginLeft: 8, fontWeight: 600, color: '#60735f', textTransform: 'none', letterSpacing: 0 }}>Saving…</span>}
                  {fwdSave?.status === 'ok' && <span style={{ marginLeft: 8, fontWeight: 700, color: '#2d6a2d', textTransform: 'none', letterSpacing: 0 }}>Saved ✓</span>}
                  {fwdSave?.status === 'err' && <span style={{ marginLeft: 8, fontWeight: 700, color: '#c0392b', textTransform: 'none', letterSpacing: 0 }}>Save failed — reload to check</span>}
                </div>
                <div style={{ fontSize: 11, color: '#60735f', marginTop: 3 }}>
                  % allocates your <strong>unpriced bushels</strong> across delivery months. 100% on July = "price all my unpriced bushels at July's net price." Caps at 100% per crop.
                </div>
              </div>
              {liveFutures?.grain_as_of && (
                <div style={{ textAlign: 'right', fontSize: 10, color: '#60735f', whiteSpace: 'nowrap' }}>
                  <div style={{ color: '#2d6a1a', fontWeight: 700 }}>● Live · USDA settled {liveFutures.grain_as_of}</div>
                  {liveFutures.ratio && (
                    <div style={{ marginTop: 2 }}>
                      Corn/Soy ratio <strong style={{ color: '#1a4a7a', fontVariantNumeric: "tabular-nums" }}>{gFmtD(liveFutures.ratio.value)}</strong> · {liveFutures.ratio.signal}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(3, marketingCrops.length)}, 1fr)`, gap: 12 }}>
              {marketingCrops.map(crop => {
                const curve = getFwdCurve(crop);
                const fa = fwdAvgPrice(crop);
                // Total unpriced bushels = open (uncontracted) + bushels under
                // contract that aren't priced yet (Bushels Only / Basis-no-cash).
                // The forward curve allocates THESE bushels — so 100% on Jul
                // means "I'd price all my unpriced bushels at July's net price."
                const expected = totalBu(crop);
                const open = expected > 0 ? Math.max(0, expected - totalCommitted(crop)) : 0;
                const unpricedFromContracts = unpricedCommittedBushels(crop);
                const unpricedTotal = open + unpricedFromContracts;
                const totalPct = curve.reduce((s, r) => s + (Number(r.pct) || 0), 0);
                const overCap = totalPct > 100;
                return (
                  <div key={crop} style={{ background: '#f9fcf6', border: `1px solid ${overCap ? '#c0392b' : grainCropColor(crop) + '33'}`, borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: grainCropColor(crop), fontFamily: "'Lora',serif" }}>{crop}</span>
                      <span style={{ fontSize: 10, color: overCap ? '#c0392b' : (totalPct === 100 ? '#2d6a1a' : '#60735f'), fontWeight: 700 }}>
                        {totalPct}% allocated
                      </span>
                    </div>
                    {/* ⚠ THIS TOTAL IS A SUM OF TWO THINGS THE CARD ABOVE STATES SEPARATELY,
                        and until it showed its own arithmetic the two screens could not be
                        reconciled by anyone reading them. Kevin presented RAUBERDALE FARMS on
                        2026-09-10: this card said 19,389 unpriced bu, the position card said
                        `Unpriced (committed) 5,000`, and both were right — 34,389 expected less
                        20,000 committed is 14,389 open, plus one 5,000 bu Bushels Only contract.
                        The explanation lived in the code comment three lines up and nowhere a
                        grower could see it.

                        The component names are the SAME WORDS the position card uses for its own
                        two rows, deliberately — a reader has to be able to look from one to the
                        other. Do not shorten them to `open` and `unpriced`. */}
                    <div style={{ fontSize: 10, color: '#60735f', marginBottom: 6 }}>
                      {(() => {
                        if (!(unpricedTotal > 0)) return <span style={{ fontStyle: 'italic' }}>no unpriced bushels</span>;
                        const bu = (n) => Math.round(n).toLocaleString();
                        const parts = [];
                        if (open > 0) parts.push(`${bu(open)} open (uncontracted)`);
                        if (unpricedFromContracts > 0) parts.push(`${bu(unpricedFromContracts)} unpriced (committed)`);
                        return (
                          <>
                            <b style={{ color: '#1a2e1a' }}>{bu(unpricedTotal)}</b> unpriced bu to allocate
                            {parts.length ? <span> = {parts.join(' + ')}</span> : null}
                            {/* ⚠ WITH NO PRODUCTION PLAN, `open` IS ZERO BY CONSTRUCTION, not because
                                the farm has none — so the total is the contract side alone and silently
                                understates what there is to allocate. Say so rather than let a reader
                                take an unstated floor for a measurement. */}
                            {expected > 0 ? null : (
                              <span style={{ color: '#a06a00' }}> · no expected production entered, so uncontracted bushels are not counted here</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {/* Value at: pull basis from Howlett's board by term + destination.
                        Pre-fills the Basis column below (still editable, to price elsewhere). */}
                    {(boardLocations(crop, 'pickup').length + boardLocations(crop, 'delivered').length) > 0 && (() => {
                      const sel = fwdVal[crop] || {};
                      const btn = (on) => ({ padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', border: '1px solid', background: on ? '#2d4a2d' : '#fff', color: on ? '#fff' : '#4a6e4a', borderColor: on ? '#2d4a2d' : '#c8d8c0', fontFamily: 'inherit' });
                      const locs = boardLocations(crop, sel.term || 'delivered');
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', marginBottom: 6, fontSize: 10, color: '#60735f' }}>
                          <span style={{ fontWeight: 700 }}>Value at:</span>
                          {boardLocations(crop, 'pickup').length > 0 && <button style={btn(sel.term === 'pickup')} onClick={() => setFwdVal(prev => ({ ...prev, [crop]: { term: 'pickup', location: null } }))}>Pickup</button>}
                          {boardLocations(crop, 'delivered').length > 0 && <button style={btn(sel.term === 'delivered')} onClick={() => setFwdVal(prev => ({ ...prev, [crop]: { term: 'delivered', location: null } }))}>Delivered</button>}
                          {sel.term && (
                            <select value={sel.location || ''} onChange={e => applyBoardValuation(crop, sel.term, e.target.value)}
                              style={{ padding: '2px 6px', borderRadius: 5, border: '1px solid #c8d8c0', fontSize: 10, fontFamily: 'inherit' }}>
                              <option value="">{sel.term === 'pickup' ? '— pickup zone —' : '— destination —'}</option>
                              {locs.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                          )}
                          {sel.location && <button style={btn(false)} onClick={() => setFwdVal(prev => ({ ...prev, [crop]: null }))} title="Back to manual basis">✕ manual</button>}
                          {sel.location && <span style={{ color: '#2d6a1a', fontWeight: 600 }}>filled from {sel.location} — edit any basis to price elsewhere</span>}
                        </div>
                      );
                    })()}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead><tr>{['Month', '%', 'Basis', 'Futures', 'Net'].map(h => (
                        <th key={h} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 800, color: '#60735f', textTransform: 'uppercase', borderBottom: '1px solid #dde8d8', textAlign: h === 'Month' ? 'left' : 'right' }}>{h}</th>
                      ))}</tr></thead>
                      <tbody>{curve.map(row => {
                        const np = row.futures + row.basis;
                        const monthBushels = unpricedTotal * (row.pct || 0) / 100;
                        return (
                          <tr key={row.month}>
                            <td style={{ padding: '3px 4px', fontSize: 10, color: '#60735f', whiteSpace: 'nowrap' }}
                              title={monthBushels > 0 ? `${Math.round(monthBushels).toLocaleString()} bu at this month` : ''}>
                              {row.month}
                            </td>
                            <td style={{ padding: '3px 4px' }}>
                              {/* Controlled input — value comes from state so a clamp on entry shows immediately */}
                              <input type="number" min="0" max="100" step="1"
                                value={row.pct === 0 ? '' : row.pct}
                                onChange={e => handleFwdCurveChange(crop, row.month, 'pct', e.target.value)}
                                style={{ ...S.inputSm, width: 40, textAlign: 'right' }} />
                            </td>
                            <td style={{ padding: '3px 4px' }}>
                              <input type="number" step="0.01"
                                key={`b-${crop}-${row.month}-${fwdVal[crop]?.location || 'manual'}-${row.basis}`}
                                defaultValue={row.basis}
                                style={{ ...S.inputSm, width: 50, textAlign: 'right' }}
                                onBlur={e => handleFwdCurveChange(crop, row.month, 'basis', e.target.value)} />
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right', fontVariantNumeric: "tabular-nums", color: '#4a6e4a', whiteSpace: 'nowrap' }}
                              title="Live daily settle — updates every trading day, not editable">
                              {gFmtD(row.futures)}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: "tabular-nums", color: '#2d6a1a' }}>{gFmtD(np)}</td>
                          </tr>
                        );
                      })}</tbody>
                      <tfoot><tr style={{ background: `${grainCropColor(crop)}11`, borderTop: '1px solid #dde8d8' }}>
                        <td colSpan={4} style={{ padding: '5px 4px', fontSize: 10, color: '#4a6e4a', fontWeight: 700 }}>Wtd avg</td>
                        <td style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 800, fontVariantNumeric: "tabular-nums", color: '#2d6a1a' }}>{fa ? gFmtD(fa) : '—'}</td>
                      </tr></tfoot>
                    </table>
                    {unpricedTotal > 0 && (
                      <div style={{ fontSize: 10, color: '#60735f', marginTop: 6, textAlign: 'right' }}>
                        Unpriced @ wtd avg: <strong style={{ color: '#1a4a7a', fontVariantNumeric: "tabular-nums" }}>{fa ? gFmtBig(unpricedTotal * fa) : '—'}</strong>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', color: '#4a6e4a', textTransform: 'uppercase', margin: '4px 0 10px', borderTop: '1px solid #e4ece4', paddingTop: 14 }}>Financial Summary <span style={{ color: '#677167', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>· all crops combined</span></div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(3, marketingCrops.length)}, 1fr)`, gap: 12, marginBottom: 16 }}>
            <div style={S.card}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 10 }}>Estimated Revenue</div>
              {marketingCrops.map(crop => (<div key={crop} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}><span style={{ color: '#60735f' }}>{crop}</span><span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{gFmtBig(cropRevenue(crop))}</span></div>))}
              <div style={{ borderTop: '1px solid #dde8d8', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 800, fontSize: 13 }}>Total</span>
                <span style={{ fontWeight: 800, fontSize: 16, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{gFmtBig(totalRevenue)}</span>
              </div>
              <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>contracted + fwd curve estimate</div>
            </div>
            <div style={S.card}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 10 }}>Estimated Expenses</div>
              {marketingCrops.map(crop => (<div key={crop} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}><span style={{ color: '#60735f' }}>{crop}</span><span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{getCost(crop) > 0 ? gFmtBig(cropExpenses(crop)) : '—'}</span></div>))}
              <div style={{ borderTop: '1px solid #dde8d8', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 800, fontSize: 13 }}>Total</span>
                <span style={{ fontWeight: 800, fontSize: 16, fontVariantNumeric: "tabular-nums", color: '#c8860a' }}>{hasCosts() ? gFmtBig(totalExpenses) : '—'}</span>
              </div>
              <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>{hasCosts() ? 'based on cost/acre inputs' : 'Enter cost/acre in Crop Plan'}</div>
            </div>
            <div style={S.card}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 10 }}>Gross Profit</div>
              {marketingCrops.map(crop => { const gp = cropGP(crop); return (<div key={crop} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}><span style={{ color: '#60735f' }}>{crop}</span><span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: hasCosts() ? gpColor(gp) : '#1a2e1a' }}>{hasCosts() ? gFmtBig(gp) : '—'}</span></div>); })}
              <div style={{ borderTop: '1px solid #dde8d8', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 800, fontSize: 13 }}>Total</span>
                <span style={{ fontWeight: 800, fontSize: 16, fontVariantNumeric: "tabular-nums", color: hasCosts() ? gpColor(totalGP) : '#1a2e1a' }}>{hasCosts() ? gFmtBig(totalGP) : '—'}</span>
              </div>
              <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>{hasCosts() && totalAcres ? gFmtBig(totalGP / totalAcres) + '/ac avg' : 'Enter cost/acre to calculate'}</div>
            </div>
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 12 }}>Recent Contracts</div>
            {contracts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#60735f', fontSize: 13 }}>No contracts yet for {cropYear}. Add your first contract above.</div>
            ) : [...contracts].slice(0, 8).map(c => {
              const np = netPrice(c);
              return (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f4f0' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: grainCropColor(c.crop), flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1a2e1a' }}>{c.crop}</span>
                    {c.contract_number && <span style={{ fontSize: 11, color: '#a8b8a8', marginLeft: 6, fontVariantNumeric: "tabular-nums" }}>#{c.contract_number}{c.pricing_line_id ? ` · ${c.pricing_line_id.replace(/^.*-/, '')}` : ''}</span>}
                    <span style={{ fontSize: 12, color: '#60735f', marginLeft: 8 }}>{cBushels(c).toLocaleString()} bu @ {gFmtD(np)}</span>
                    <span style={{ ...S.typeBadge(cType(c)), marginLeft: 8 }}>{cType(c)}</span>
                    {c.buyer && <span style={{ fontSize: 11, color: '#60735f', marginLeft: 8 }}>· {c.buyer}</span>}
                  </div>
                  {/* Same treatment as the table: a bordered, labelled control
                      rather than a pale glyph that reads as decoration. */}
                  <button title="Edit this contract" onClick={() => { setEditingContract(c); setContractForm({ ...c }); setModal('contract'); }}
                    style={{ background: '#fff', border: '1px solid #cfd8cf', borderRadius: 6, cursor: 'pointer',
                             fontSize: 12, fontWeight: 700, color: '#2d4a2d', padding: '3px 9px', whiteSpace: 'nowrap' }}>✎ Edit</button>
                </div>
              );
            })}
          </div>
        </>}

        {tab === 'contracts' && <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {['All', ...marketingCrops].map(c => (<button key={c} onClick={() => setCropFilter(c)} style={S.tag(cropFilter === c)}>{c}</button>))}

            <div style={{ flex: 1 }} />
            {/* PRIMARY action, and it sits with the contracts rather than in the
                page header where it competed with Edit Futures / ← Farms and read
                as one more chrome button. Heavier than the outline actions beside
                it so the eye lands here first. Pre-selects whatever crop is
                filtered, since that's the one being worked on. */}
            <button onClick={() => openNewContract(cropFilter === 'All' ? 'Corn' : cropFilter)}
              style={{ ...S.btnGreen, fontSize: 13, fontWeight: 800, padding: '8px 18px', boxShadow: '0 1px 3px rgba(26,46,26,0.18)' }}>
              + Add Contract
            </button>
            <button style={S.btnSmOut} onClick={() => setTab('import')} title="Import contracts or scale tickets">⬆ Import…</button>
            <button style={{ ...S.btnSmOut, ...(hideFilled ? { background: '#2d6a2d', color: '#fff', borderColor: '#2d6a2d' } : {}) }} onClick={() => setHideFilled(v => !v)}>{hideFilled ? '☑ Filled hidden' : '☐ Hide filled'}</button>
            <button style={S.btnSmOut} onClick={exportCSV}>⬇ Export CSV</button>
          </div>
          <div style={{ background: '#f4f8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
            {(cropFilter === 'All' ? marketingCrops : [cropFilter]).map(crop => {
              const del = deliveredBu(crop), open = contractDelivery['open_' + crop] || 0;
              return (<div key={crop}><strong style={{ color: grainCropColor(crop) }}>{crop}</strong>: {gFmtBu(soldBu(crop))} sold{totalBu(crop) ? ` / ${gFmtPct(pctSold(crop))} of plan` : ''}{del > 0 ? <span style={{ color: '#5c6f5c' }}> · {gFmtBu(del)} delivered{open > 0 ? <span style={{ color: '#a06a00', fontWeight: 700 }}> · {gFmtBu(open)} open storage</span> : ''}</span> : ''}</div>);
            })}
          </div>
          <div style={{ background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, overflow: 'hidden', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              {/* WHERE IT GOES IS NOT WHO BOUGHT IT, and the book needs both.
                  R L Jeffres sells wheat to Howlett Farms delivered to STAR OF
                  THE WEST — two different companies on one contract, and until
                  2026-09-01 only the buyer was on screen. It is not a caption:
                  Howlett posts basis PER DELIVERY POINT ("Avon & Lakeville",
                  "LeRoy & TD Ag", "Batavia", "P/U West"), so a contract whose
                  delivery point you cannot see cannot be checked against the
                  bid it was written off, and the Cash Targets engine keys on
                  exactly this. */}
              <thead><tr>{['Contract #', 'Crop', 'Buyer', 'Delivery', 'Bushels', 'Delivered', 'Type', 'Futures', 'Basis', 'Net Price', 'Written', 'Ship Month', 'Futures Mo.', 'Del. Start', 'Del. End', 'Notes', 'Edit'].map(h => {
                // 'Edit' is the pinned actions column, not a field: it has nothing
                // to sort by, and the empty-string test alone stopped covering it the
                // moment the header gained a label.
                const sortable = h !== '' && h !== 'Edit';
                const active = sortCol === h;
                return (
                  <th key={h} onClick={sortable ? () => toggleSort(h) : undefined} title={sortable ? `Sort by ${h}` : ''}
                    style={{ ...S.th, cursor: sortable ? 'pointer' : 'default', userSelect: 'none',
                      color: active ? '#1a2e1a' : S.th.color, background: active ? '#e3eede' : S.th.background,
                      // The actions column is PINNED. It is the last of fifteen
                      // columns in a horizontally scrolling table, so unpinned it
                      // is off-screen at every normal window width.
                      ...(h === 'Edit' ? { position: 'sticky', right: 0, zIndex: 3,
                        background: '#eef2ec', borderLeft: '1px solid #cfd8cf', textAlign: 'center' } : {}) }}>
                    {h}{active ? <span style={{ marginLeft: 3 }}>{sortDir === 'asc' ? '▲' : '▼'}</span> : (sortable ? <span style={{ marginLeft: 3, opacity: 0.28, fontSize: 9 }}>▲▼</span> : null)}
                  </th>
                );
              })}</tr></thead>
              <tbody>
                {(() => {
                  let rows = (cropFilter === 'All' ? contracts : cropContracts(cropFilter)).filter(c => !hideFilled || !c.filled);
                  // Drop Bushel's redundant zero-bushel header line (the P00 line when the
                  // pricing lives on P01+). A zero-bushel line is only hidden if its contract
                  // has another line carrying the bushels — a contracted-but-fully-unpriced
                  // contract still shows its single row so it never disappears.
                  const nonzeroByContract = {};
                  rows.forEach(c => { if (c.contract_number && cBushels(c) > 0) nonzeroByContract[c.contract_number] = true; });
                  rows = rows.filter(c => cBushels(c) > 0 || !(c.contract_number && nonzeroByContract[c.contract_number]));
                  if (sortCol) rows = [...rows].sort(contractCmp);
                  return rows;
                })().map((c, i) => {
                  const np = netPrice(c);
                  const ct = cType(c);
                  const ib = ct === 'Basis' || ct === 'Bushels Only';
                  const htaPh = htaBasisUnfixed(c) ? htaPlaceholder(c) : null;   // HTA priced with a placeholder curve basis
                  return (
                    <tr key={c.id} style={{ background: c.filled ? '#f1f3ef' : (i % 2 ? '#fafcf8' : '#fff'), opacity: c.filled ? 0.72 : 1 }}>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", fontWeight: 700, color: '#1a2e1a' }}>
                        {c.contract_number || '—'}
                        {c.pricing_line_id && <span style={{ marginLeft: 4, color: '#a8b8a8', fontWeight: 500 }}>·{c.pricing_line_id.replace(/^.*-/, '')}</span>}
                        {c.filled && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#2d6a2d', background: '#e6f4e0', borderRadius: 10, padding: '1px 7px', letterSpacing: '0.04em' }}>✓ FILLED</span>}
                        {c.source === 'manual' && c.pricing_line_id && (
                          <span title="Hand-edited — farmer-owned, protected from CSV imports (the elevator export won't overwrite or delete it)." style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#1a4a7a', background: '#e6eef6', borderRadius: 10, padding: '1px 7px', letterSpacing: '0.04em' }}>✏️ EDITED</span>
                        )}
                      </td>
                      <td style={{ ...S.td, fontWeight: 700, color: grainCropColor(c.crop) }}>{c.crop}{grainProgramChip(c.crop_program)}</td>
                      <td style={S.td}>{c.buyer || '—'}</td>
                      {/* A DASH HERE IS A REAL HOLE, NOT A TIDY BLANK. Every one
                          of the 621 imported contracts carries a delivery point;
                          17 of the 20 hand-entered ones do not, because the entry
                          form had no field for it until now. Muting that would
                          hide the gap the form just stopped creating. */}
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        {c.location
                          ? <span title={`Delivered to ${c.location}`}>{c.location}</span>
                          : <span style={{ color: '#b9895a' }} title="No delivery point recorded. Basis is posted per delivery point, so this contract cannot be checked against the bid it was written off.">not recorded</span>}
                      </td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right' }}>{cBushels(c).toLocaleString()}</td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', fontSize: 11, minWidth: 122 }}>
                        {(() => {
                          const del = contractDelivery[c.id] || 0; const cap = cBushels(c);
                          const fill = cap > 0 ? Math.round(del / cap * 100) : 0;
                          const rem = Math.max(0, cap - del);
                          if (del <= 0) return <span style={{ color: '#c8d2c8' }}>—</span>;
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                <span style={{ color: fill >= 100 ? '#2f8a48' : '#a06a00', fontWeight: 700 }}>{Math.round(del).toLocaleString()}</span>
                                <span style={{ color: '#677167', fontWeight: 400 }}>{fill}%</span>
                              </div>
                              <GrainProgressBar pct={fill} height={5} color={fill >= 100 ? '#2f8a48' : '#e0a52b'} />
                              <span style={{ fontSize: 10.5, color: rem > 0 ? '#a06a00' : '#677167', fontWeight: rem > 0 ? 700 : 400 }}>{rem > 0 ? `${Math.round(rem).toLocaleString()} bu left` : '✓ complete'}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td style={S.td}><span style={S.typeBadge(ct)}>{ct}</span></td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right' }}>
                        {ib
                          ? <span style={{ color: '#aaa' }}>mkt {gFmtD(getFutures(c.crop).price)}</span>
                          : c.futures_derived
                            ? <span title="The elevator's file states no futures price on this line — it gives basis and cash. This is cash − basis, so the contract is genuinely locked at it, but the futures leg is our arithmetic, not theirs." style={{ color: '#1a4a7a' }}>{gFmtD(cFutures(c))}<span style={{ fontSize: 10, marginLeft: 3, fontWeight: 700 }}>calc</span></span>
                            : gFmtD(cFutures(c))}
                      </td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right' }}>
                        {htaPh
                          ? <span title={`Placeholder basis from the forward price curve${htaPh.month ? ` (${htaPh.month} month)` : ''} — this HTA's basis isn't locked yet.`} style={{ color: '#a06a00', fontStyle: 'italic' }}>~{htaPh.basis >= 0 ? '+' : ''}{htaPh.basis.toFixed(2)}</span>
                          : `${Number(c.basis) >= 0 ? '+' : ''}${Number(c.basis || 0).toFixed(2)}`}
                      </td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", fontWeight: 800, textAlign: 'right', color: htaPh ? '#a06a00' : '#1a6a3a' }}
                        title={htaPh ? `Estimated cash price — placeholder basis from the forward curve${htaPh.month ? ` (${htaPh.month} month)` : ''}, HTA basis not yet locked.` : undefined}>
                        {gFmtD(np)}{htaPh ? '*' : ''}
                      </td>
                      <td style={{ ...S.td, color: '#60735f', whiteSpace: 'nowrap' }} title="Contract written / created date">{c.contract_date ? new Date(c.contract_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}</td>
                      <td style={{ ...S.td, color: c.ship_month ? '#1a2e1a' : '#bbb', fontWeight: c.ship_month ? 600 : 400 }}>{c.ship_month || '—'}</td>
                      <td style={{ ...S.td, color: '#60735f', fontSize: 11 }}>{c.futures_month || c.fmonth || '—'}</td>
                      <td style={{ ...S.td, color: '#60735f' }}>{c.delivery_start || c.dstart || '—'}</td>
                      <td style={{ ...S.td, color: '#60735f' }}>{c.delivery_end || c.dend || '—'}</td>
                      <td style={{ ...S.td, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#60735f' }} title={c.notes}>{c.notes || ''}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap', position: 'sticky', right: 0, zIndex: 2,
                                   background: '#fff', borderLeft: '1px solid #e3eade', textAlign: 'center' }}>
                        <button title={c.filled ? 'Mark as open (not yet filled)' : 'Mark as filled / delivered'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14,
                                   color: c.filled ? '#2d6a2d' : '#677167', padding: '2px 5px' }}
                          onClick={() => handleToggleFilled(c)}>{c.filled ? '✓' : '○'}</button>
                        {/* A real button, not a glyph: bordered, labelled and in
                            the body text colour, so it reads as something you
                            can press. */}
                        <button title="Edit this contract"
                          style={{ background: '#fff', border: '1px solid #cfd8cf', borderRadius: 6,
                                   cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#2d4a2d',
                                   padding: '3px 9px', margin: '0 4px' }}
                          onClick={() => { setEditingContract(c); setContractForm({ ...c }); setModal('contract'); }}>✎ Edit</button>
                        <button title="Delete this contract"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13,
                                   color: '#c0392b', padding: '2px 5px' }}
                          onClick={() => handleDeleteContract(c.id)}>✕</button>
                      </td>
                    </tr>
                  );
                })}
                {/* Unsold-bushels rows removed per user request — that detail
                    lives on the Dashboard now (Open / Unpriced bushel counts
                    + the forward-curve scenario). */}
                {false && (cropFilter === 'All' ? marketingCrops : [cropFilter]).map(crop => {
                  const u = unsoldBu(crop); if (!u || u <= 0) return null;
                  const fa = fwdAvgPrice(crop), val = fa ? u * fa : u * getFutures(crop).price;
                  return (
                    <tr key={crop + '_unsold'} style={{ background: '#fff8f8' }}>
                      <td style={S.td}>—</td>
                      <td style={{ ...S.td, fontWeight: 700, color: '#c0392b' }}>{crop}</td>
                      <td colSpan={2} style={{ ...S.td, color: '#c0392b', fontStyle: 'italic' }}>{gFmtBu(u)} unpriced</td>
                      <td style={S.td}>—</td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', color: '#c0392b' }}>{gFmtD(getFutures(crop).price)}</td>
                      <td style={S.td}>—</td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', color: '#c0392b' }}>{fa ? gFmtD(fa) + ' (fwd)' : gFmtD(getFutures(crop).price)}</td>
                      <td colSpan={5} style={{ ...S.td, color: '#c0392b', fontStyle: 'italic' }}>est. {gFmtBig(val)} unhedged</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {contracts.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: '#60735f', fontSize: 13 }}>No contracts for {cropYear}.</div>}
          </div>
          {(cropFilter === 'All' ? contracts : cropContracts(cropFilter)).some(htaBasisUnfixed) && (
            <div style={{ fontSize: 11, color: '#a06a00', marginTop: 8 }}>
              <b>*</b> HTA net price is an <b>estimate</b> — the basis isn't locked yet, so it's priced with a <b>placeholder basis</b> (<span style={{ fontStyle: 'italic' }}>~</span>) pulled from your Forward Price Curve. Set the basis on the contract once fixed to replace it.
            </div>
          )}
        </>}

        {tab === 'tickets' && <>
          {/* Scale tickets — every delivered load for the selected crop-year. Tickets
              carry no contract id; they FIFO-fill contracts (see the Contracts tab's
              Delivered column) and any overflow beyond contracted bushels is open storage. */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {['All', ...marketingCrops].map(c => (<button key={c} onClick={() => setCropFilter(c)} style={S.tag(cropFilter === c)}>{c}</button>))}
            <div style={{ flex: 1 }} />
            <button style={S.btnSmOut} onClick={() => setTab('import')} title="Import scale tickets">⬆ Import…</button>
          </div>

          {/* Per-crop delivery progress vs contracted bushels */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${(cropFilter === 'All' ? marketingCrops : [cropFilter]).length}, 1fr)`, gap: 12, marginBottom: 14 }}>
            {(cropFilter === 'All' ? marketingCrops : [cropFilter]).map(crop => {
              const cropTickets = tickets.filter(t => t.crop === crop && t.crop_year === cropYear);
              const del = cropTickets.reduce((s, t) => s + (Number(t.net_bushels) || 0), 0);
              const contracted = cropContracts(crop).reduce((s, c) => s + cBushels(c), 0);
              const open = contractDelivery['open_' + crop] || 0;
              const pct = contracted > 0 ? Math.round(del / contracted * 100) : (del > 0 ? 100 : 0);
              const filled = Math.min(del, contracted);
              const toDeliver = Math.max(0, contracted - del);
              return (
                <div key={crop} style={{ background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, padding: '12px 16px', borderLeft: `4px solid ${grainCropColor(crop)}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: grainCropColor(crop), fontFamily: "'Lora',serif" }}>{crop}</span>
                    <span style={{ fontSize: 11, color: '#60735f' }}>{cropTickets.length} load{cropTickets.length === 1 ? '' : 's'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#1a2e1a', fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{del > 0 ? Math.round(del).toLocaleString() : '—'}<span style={{ fontSize: 11, color: '#60735f', fontWeight: 600, marginLeft: 3 }}>bu</span></span>
                    <span style={{ fontSize: 11, color: '#60735f' }}>{contracted > 0 ? `of ${Math.round(contracted).toLocaleString()} contracted` : 'no contracts'}</span>
                  </div>
                  {contracted > 0 ? (
                    <>
                      <GrainProgressBar pct={pct} height={8} color="#2d6a2d" over={open > 0} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginTop: 5 }}>
                        <span style={{ color: '#2d6a2d', fontWeight: 700 }}>{Math.round(filled).toLocaleString()} on contract</span>
                        {open > 0
                          ? <span style={{ color: '#a06a00', fontWeight: 800 }}>+{Math.round(open).toLocaleString()} open storage</span>
                          : <span style={{ color: '#677167' }}>{toDeliver > 0 ? `${Math.round(toDeliver).toLocaleString()} bu left to deliver` : '✓ fully delivered'}</span>}
                      </div>
                    </>
                  ) : (
                    del > 0 && <div style={{ fontSize: 10, color: '#a06a00', fontWeight: 700 }}>all in open storage — no contracts on file</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Ticket table */}
          {(() => {
            const rows = tickets.filter(t => t.crop_year === cropYear && (cropFilter === 'All' || t.crop === cropFilter));
            if (!rows.length) return (
              <div style={{ ...S.card, textAlign: 'center', padding: '40px 24px', border: '2px dashed #c8d8c0' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🎫</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2e1a', marginBottom: 4 }}>No scale tickets for {cropFilter === 'All' ? '' : cropFilter + ' '}{cropYear}</div>
                <div style={{ fontSize: 12, color: '#60735f', marginBottom: 14 }}>Import a Bushel scale-ticket export to see every delivered load here.</div>
                <button onClick={() => setTab('import')} style={S.btnGreen}>⬆ Import scale tickets</button>
              </div>
            );
            const totNet = rows.reduce((s, t) => s + (Number(t.net_bushels) || 0), 0);
            const totGross = rows.reduce((s, t) => s + (Number(t.gross_bushels) || 0), 0);
            const rightCols = ['Gross bu', 'Net bu', 'Moisture', 'Test wt'];
            const fmtN = (v, d = 0) => v == null || v === '' || isNaN(Number(v)) ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
            return (
              <div style={{ background: '#fff', border: '1px solid #dde8d8', borderRadius: 10, overflow: 'hidden', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>{['Date', 'Ticket #', 'Crop', 'Buyer', 'Location', 'Contract', ...rightCols].map(h => (<th key={h} style={{ ...S.th, textAlign: rightCols.includes(h) ? 'right' : 'left' }}>{h}</th>))}</tr></thead>
                  <tbody>
                    {rows.map((t, i) => {
                      const contractMatch = contracts.find(c => c.contract_number && t.contract_ref && String(c.contract_number) === String(t.contract_ref));
                      return (
                        <tr key={t.id || (t.ticket_id + '-' + i)} style={{ background: i % 2 ? '#fafcf8' : '#fff' }}>
                          <td style={{ ...S.td, whiteSpace: 'nowrap', color: '#4a6e4a' }}>{t.delivery_date ? new Date(t.delivery_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}</td>
                          <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", color: '#1a2e1a' }}>{t.ticket_id || '—'}</td>
                          <td style={{ ...S.td, fontWeight: 700, color: grainCropColor(t.crop) || '#1a2e1a' }}>{t.crop}</td>
                          <td style={{ ...S.td, color: '#4a6e4a' }}>{t.buyer || '—'}</td>
                          <td style={{ ...S.td, color: '#60735f' }}>{t.location || '—'}</td>
                          <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", color: contractMatch ? '#2d5a10' : '#677167' }} title={contractMatch ? `Matches contract ${contractMatch.contract_number} on file` : t.contract_ref ? 'No matching contract on file' : ''}>{t.contract_ref || '—'}{contractMatch ? ' ✓' : ''}</td>
                          <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', color: '#60735f' }}>{fmtN(t.gross_bushels)}</td>
                          <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', fontWeight: 800, color: '#1a2e1a' }}>{fmtN(t.net_bushels)}</td>
                          <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', color: '#60735f' }}>{t.moisture != null && t.moisture !== '' ? fmtN(t.moisture, 1) + '%' : '—'}</td>
                          <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', color: '#60735f' }}>{t.test_weight != null && t.test_weight !== '' ? fmtN(t.test_weight, 1) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f4f8f0', borderTop: '2px solid #dde8d8' }}>
                      <td style={{ ...S.td, fontWeight: 800, color: '#1a2e1a' }} colSpan={6}>{rows.length} load{rows.length === 1 ? '' : 's'} · {cropFilter === 'All' ? 'all crops' : cropFilter} · {cropYear}</td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', fontWeight: 700, color: '#60735f' }}>{fmtN(totGross)}</td>
                      <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', fontWeight: 800, color: '#1a2e1a' }}>{fmtN(totNet)}</td>
                      <td style={S.td} colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
        </>}

        {tab === 'plan' && <>
          {/* Storage capacity lives here, not on the Summary (moved
              2026-08-28). It is a farm FACT the grower states, and the Summary
              reads it to work out how many bushels have nowhere to go at
              harvest — so it belongs with acres, yield and chopped acres, not
              beside a market read. `storage` is resolved straight from the
              farmer's own metadata rather than from actionResult, so the
              control is present even for a crop whose Summary has not loaded. */}
          {grainActionFarmer && (
            <div style={{ marginBottom: 16 }}>
              <GrainStorageEditor farmer={grainActionFarmer} storage={resolveGrainStorage(grainActionFarmer.metadata)} onSaved={onGrainStorageSaved} />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(3, marketingCrops.length)}, 1fr)`, gap: 12, marginBottom: 16 }}>
            {marketingCrops.map(crop => {
              const plan = getPlan(crop), s = soldBu(crop), t = totalBu(crop), u = unsoldBu(crop), fa = fwdAvgPrice(crop), cpa = getCost(crop);
              const te = cropExpenses(crop), gp = cropGP(crop), gpa = cropGPA(crop), hc = cpa > 0, curve = getFwdCurve(crop);
              return (
                <div key={`${crop}-${cropYear}`} style={S.card}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: grainCropColor(crop), marginBottom: 12, fontFamily: "'Lora',serif" }}>{crop} <span style={{ fontSize: 11, color: '#60735f', fontWeight: 400 }}>{cropYear}</span></div>
                  <div style={{ marginBottom: 8 }}><label style={S.label}>Acres Planted</label><PlanNumberInput type="number" key={`ac-${crop}-${cropYear}`} seed={plan.acres ? Math.round(plan.acres * 100) / 100 : ''} min="0" step="0.01" placeholder="0" style={S.input} onChange={e => handlePlanChange(crop, 'acres', e.target.value)} /></div>
                  {/* CHOPPED ACRES — grain marketing only, and CORN ONLY. Acres RMA
                      insured as GRAIN corn that will go through a chopper: they grow
                      a crop but deliver no bushels into a contract. Default 0 leaves
                      the insurance guarantee exactly as the policy states it, which
                      is the behaviour every farm that does not chop keeps.

                      This box used to render for every crop in GRAIN_CROPS, because
                      it sat inside the map with no crop test at all. Soybeans and
                      wheat are not chopped for silage, and the control invited a
                      grower to shorten a sell ceiling for a reason that does not
                      exist. cropChopsForSilage is the one rule; choppedAcresFor
                      refuses a non-corn crop too, so hiding the box is the visible
                      half rather than the whole guard. */}
                  {cropChopsForSilage(crop) && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={S.label}>Chopped for silage (ac)</label>
                    <PlanNumberInput type="number" min="0" step="0.01" key={`chop-${crop}-${cropYear}`}
                      seed={plan.chopped ? Math.round(plan.chopped * 100) / 100 : ''} placeholder="0"
                      style={S.input}
                      onChange={e => handlePlanChange(crop, 'chopped_acres', e.target.value)} />
                    <div style={{ fontSize: 10, color: plan.chopped > 0 ? '#a06a00' : '#677167', marginTop: 3, lineHeight: 1.4 }}>
                      {plan.chopped > 0
                        ? <>Insured as grain, chopped anyway — <b>{gFmtAc(plan.grainAcres)} ac</b> left to deliver bushels. Grain marketing only; the policy still covers all {gFmtAc(plan.acres)} ac.</>
                        : <>Acres insured as GRAIN that you&apos;ll chop. Lowers the sell ceiling here only — nothing else in SILAS moves.</>}
                    </div>
                  </div>
                  )}
                  {/* ── Yield: APH by default, typed estimate, then actual ──────────
                      Two separate boxes on purpose. The estimate box is BLANK when
                      the APH is carrying it (the APH shows as the placeholder), so
                      "nothing typed" reads as "using the APH" instead of looking
                      like a keyed number. Entering actual bushels retires the
                      estimate — the chip flips to ACTUAL and every downstream
                      figure follows. */}
                  {(() => {
                    const yi = yieldInfo(crop);
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                          <label style={{ ...S.label, marginBottom: 0 }}>Yield (bu/ac)</label>
                          <YieldBasisChip info={yi} />
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: yi.value ? '#1a2e1a' : '#b3c0b0', margin: '2px 0 6px' }}>
                          {yi.value ? `${gFmtY(yi.value)} bu/ac` : 'no yield yet'}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <label style={{ ...S.label, fontSize: 10 }}>Estimate (optional)</label>
                            <PlanNumberInput type="number" min="0" key={`yest-${crop}-${cropYear}`}
                              seed={yi.keyed ?? ''}
                              placeholder={yi.aph ? `APH ${gFmtY(yi.aph)}` : '0'}
                              style={S.input}
                              onChange={e => handlePlanChange(crop, 'yield', e.target.value)} />
                          </div>
                          <div>
                            {/* TOTAL BUSHELS, not bu/ac (Kevin, 2026-08-16). The column
                                behind it is `actual_production` in bushels, so a bu/ac
                                box had to multiply by acres to store it — which made a
                                wrong acre figure corrupt actual production, and acres
                                had just been wrong book-wide by every prevent-plant
                                acre. A grower knows the bin total; taking it directly
                                removes the multiplication and the box works before
                                acres are keyed at all. */}
                            <label style={{ ...S.label, fontSize: 10 }}>Actual (total bu)</label>
                            <PlanNumberInput type="number" min="0" step="1" key={`yact-${crop}-${cropYear}`}
                              seed={yi.actualBu != null ? Math.round(yi.actualBu) : ''}
                              placeholder="total bu"
                              style={S.input}
                              onBlur={e => {
                                const b = parseFloat(e.target.value);
                                handlePlanChange(crop, { actual_production: (!isNaN(b) && b > 0) ? Math.round(b) : null });
                              }} />
                          </div>
                        </div>
                        {/* What the AIP was told, offered but never applied on its own.
                            One click adopts it; nothing is written until it is clicked,
                            because an APH database is a document the grower signed and
                            silently copying it into a marketing plan would make the two
                            impossible to tell apart later. */}
                        {(() => {
                          const rep = reportedProd[crop];
                          if (!rep) return null;
                          const same = yi.actualBu != null && Math.abs(yi.actualBu - rep.bu) < 1;
                          return (
                            <div style={{ marginTop: 6, padding: '6px 9px', borderRadius: 6, background: '#f4f8f0',
                                          border: '1px solid #dde8d8', fontSize: 10.5, lineHeight: 1.45, color: '#4a6e4a' }}>
                              <b style={{ color: '#2d4a2d' }}>Reported to crop insurance:</b>{' '}
                              <b style={{ color: '#1a2e1a' }}>{gFmtBu(rep.bu)}</b>
                              {rep.acres ? <span style={{ color: '#60735f' }}> on {gFmtAc(rep.acres)} ac · {gFmtY(rep.bu / rep.acres)} bu/ac</span> : null}
                              {same
                                ? <span style={{ color: '#2d6a1a', fontWeight: 700 }}> · in use</span>
                                : <button onClick={() => handlePlanChange(crop, { actual_production: Math.round(rep.bu) })}
                                    style={{ marginLeft: 6, cursor: 'pointer', font: 'inherit', fontWeight: 700,
                                             border: '1px solid #2d4a2d', background: '#2d4a2d', color: '#fff',
                                             borderRadius: 5, padding: '2px 8px' }}>Use this</button>}
                              <div style={{ color: '#677167', marginTop: 2 }}>
                                {rep.lines} yield line{rep.lines === 1 ? '' : 's'} on policy {rep.policy} — the APH database, not delivered bushels.
                              </div>
                            </div>
                          );
                        })()}
                        <div style={{ fontSize: 10.5, color: '#677167', marginTop: 4, lineHeight: 1.4 }}>
                          {yi.basis === 'actual'
                            ? <>Harvested {Math.round(yi.actualBu).toLocaleString()} bu ÷ {Math.round(yi.acres).toLocaleString()} ac. Clear this box to go back to the estimate.</>
                            : yi.basis === 'keyed'
                              ? <>Clear the estimate to fall back to the insurance APH{yi.aph ? ` (${gFmtY(yi.aph)})` : ''}.</>
                              : <>Using the crop-insurance APH. Type an estimate to override it, or enter actual once harvested.</>}
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{ background: '#f4f8f0', borderRadius: 7, padding: '10px 12px', marginBottom: 12, display: 'grid', gap: 4, fontSize: 11 }}>
                    {[['Total production', t ? gFmtBu(t) : '—', null], ['Contracted', gFmtBu(s), null], ['% Sold', t ? gFmtPct(pctSold(crop)) : '—', null], ['Unsold', u != null ? gFmtBu(u) : '—', '#c0392b'], ['Est. revenue', gFmtBig(cropRevenue(crop)), '#2d6a1a']].map(([lbl, val, col]) => (
                      <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#60735f' }}>{lbl}</span><span style={{ fontWeight: 700, color: col || '#1a2e1a' }}>{val}</span></div>
                    ))}
                  </div>
                  <div style={{ marginBottom: 12, paddingTop: 10, borderTop: '1px solid #dde8d8' }}>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 2 }}>Crop Insurance Guarantee</div>
                    <div style={{ fontSize: 10, color: '#677167', marginBottom: 8 }}>{plan.fromInsurance ? 'Auto-filled from crop insurance — edit to override' : 'No insurance on file for this crop — enter manually'}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      {/* Rounded for DISPLAY only. Typing here saves what was typed;
                          leaving it alone saves nothing, so the exact APH behind the
                          card is untouched — a 14-digit weighted average is residue,
                          but it is not this box's job to overwrite it. */}
                      <div><label style={S.label}>APH (bu/ac)</label><PlanNumberInput type="number" min="0" step="0.1" key={`aph-${crop}-${cropYear}`} seed={plan.aph ? Math.round(plan.aph * 10) / 10 : ''} placeholder="0" style={S.input} onChange={e => handlePlanChange(crop, 'aph_yield', e.target.value)} /></div>
                      <div><label style={S.label}>Coverage %</label><input type="number" min="0" max="100" defaultValue={plan.coverage ? Math.round(plan.coverage * 100) : ''} placeholder="0" style={S.input} onChange={e => handlePlanChange(crop, { coverage_level: e.target.value === '' ? null : Number(e.target.value) / 100 })} /></div>
                    </div>
                    <label style={S.label}>Production Guarantee (bu)</label>
                    <input type="number" min="0" defaultValue={plan.guaranteeOverride ?? ''} placeholder={(() => { const g = cropGuaranteeBu(crop); return g != null ? gFmtBu(g) : 'acres × APH × coverage'; })()} style={S.input} onChange={e => handlePlanChange(crop, 'insurance_guarantee_override', e.target.value)} />
                    {/* SAY WHICH METHOD PRODUCED THE NUMBER. This always printed
                        "(acres × APH × coverage)" — but guaranteeBushels() is
                        LIABILITY-FIRST whenever RMA has keyed liability, so the
                        parenthetical described arithmetic that had not been done.
                        Sullivan's wheat: 28,934 stated, while 482.71 × 75.042 × 80%
                        is 28,979. The gap was hidden by 14-digit operands nobody
                        multiplies by eye; rounding them would have made a false
                        equation legible. Liability ÷ projected price is RMA's own
                        acre-weighted Marketable Guarantee — the figure the SOI
                        prints — so it is the better number AND the one to name. */}
                    {(() => {
                      const g = cropGuaranteeBu(crop);
                      if (g == null) return null;
                      const ci = cropInsurance(crop);
                      const fromLiability = (ci?.guaranteeBu || 0) > 0;
                      const chop = ci?.chop;
                      return (
                        <div style={{ fontSize: 11, color: '#2d6a1a', fontWeight: 700, marginTop: 4 }}>
                          = {gFmtBu(g)} guaranteed{
                            plan.guaranteeOverride != null ? ' (override)'
                            : fromLiability ? <span style={{ fontWeight: 400, color: '#60735f' }}> — RMA liability ÷ projected price, per coverage line</span>
                            : ` (${gFmtAc(plan.grainAcres) || 0} ac × ${gFmtY(plan.aph) || 0} × ${Math.round((plan.coverage || 0) * 100)}%)`}
                          {/* NEVER let the haircut be silent. A guarantee that reads
                              short with no reason on screen is indistinguishable from
                              a dropped coverage line, which is the failure the
                              incomplete[] banner beside it exists to prevent. */}
                          {chop && chop.chopped > 0 && (
                            <div style={{ fontWeight: 400, color: '#a06a00', marginTop: 3 }}>
                              after {gFmtAc(chop.chopped)} ac chopped ({Math.round((1 - chop.share) * 100)}% of the grain acres) — the policy still guarantees the full {gFmtBu(g / (chop.share || 1))} bu
                              {chop.capped && <span style={{ color: '#a32020', fontWeight: 700 }}> · you entered {gFmtAc(chop.asked)} ac, more than the {gFmtAc(ci.acres)} ac insured as grain — capped</span>}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ marginBottom: 12, paddingTop: 10, borderTop: '1px solid #dde8d8' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 6 }}>Cost of Production</div>
                      <button onClick={() => { setTab('cop'); setCopCrop(crop); }} style={{ background: 'none', border: 'none', color: '#2d6a1a', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>🧮 open COP tool →</button>
                    </div>
                    <label style={S.label}>Cost per Acre ($)</label>
                    <PlanNumberInput type="number" key={`cost-${crop}-${cropYear}`} seed={cpa || ''} min="0" step="1" placeholder="e.g. 650" style={S.input} onChange={e => { handleCostChange(crop, e.target.value).catch(() => setCopSave({ status: 'err' })); }} />
                    {getCOP(crop).hasRow && <div style={{ fontSize: 10, color: '#2d6a1a', marginTop: 3 }}>from Cost of Production tool ({getCOP(crop).mode})</div>}
                    {hc && <div style={{ marginTop: 8, display: 'grid', gap: 3, fontSize: 11 }}>
                      {[['Total expenses', gFmtBig(te), '#8a6a00'], ['Gross profit', gFmtBig(gp), gpColor(gp)], ['GP per acre', gpa != null ? gFmtD(gpa) : '—', gpColor(gpa || 0)], ['Breakeven price', t ? gFmtD(te / t) : '—', null]].map(([lbl, val, col]) => (
                        <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#60735f' }}>{lbl}</span><span style={{ fontWeight: 700, color: col || '#1a2e1a' }}>{val}</span></div>
                      ))}
                    </div>}
                    {!hc && <div style={{ fontSize: 11, color: '#aaa', marginTop: 5 }}>Enter cost/acre to see P&L</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={S.card}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: '#4a6e4a', textTransform: 'uppercase', marginBottom: 12 }}>Unsold Position — Forward Curve Valuation</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(3, marketingCrops.length)}, 1fr)`, gap: 12 }}>
              {marketingCrops.map(crop => {
                const u = unsoldBu(crop) || 0, fa = fwdAvgPrice(crop), fv = fa && u ? u * fa : null, mv = u ? u * getFutures(crop).price : null;
                return (
                  <div key={crop} style={{ borderLeft: `4px solid ${grainCropColor(crop)}`, paddingLeft: 12, paddingTop: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: grainCropColor(crop), marginBottom: 6 }}>{crop}</div>
                    {[['Unsold bu', gFmtBu(u), '#c0392b'], ['Fwd avg price', fa ? gFmtD(fa) : '—', '#2d6a1a'], ['Fwd value', fv ? gFmtBig(fv) : '—', '#2d6a1a'], ['Mkt value (nearby)', mv ? gFmtBig(mv) : '—', null]].map(([lbl, val, col]) => (
                      <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}><span style={{ color: '#60735f' }}>{lbl}</span><span style={{ fontWeight: 700, color: col || '#1a2e1a' }}>{val}</span></div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </>}

        {tab === 'cop' && (
          !selectedFarmerId ? (
            <div style={S.card}><div style={{ color: '#60735f', fontSize: 13 }}>Select a farmer to build their cost of production.</div></div>
          ) : (() => {
            const crop = copCrop;
            const { mode, items } = getCOP(crop);
            const cats = copCats(mode, crop);
            // Group the detailed rows into budget sections (streamlined rows carry
            // no `group`, so they collapse into one unnamed section → flat render).
            const copGroups = cats.reduce((acc, c) => {
              const name = c.group || '';
              let g = acc.find(x => x.name === name);
              if (!g) { g = { name, items: [] }; acc.push(g); }
              g.items.push(c);
              return acc;
            }, []);
            const groupSum = g => g.items.reduce((s, c) => s + copSigned(mode, crop, items, c), 0);
            const total = copTotal(crop);
            const plan = getPlan(crop);
            const yld = plan.yield || plan.aph || 0;
            const costPerBu = yld ? total / yld : null;
            const acres = plan.acres || 0;
            // ── Where the divisor came from ──────────────────────────────────
            // Cost/bushel and break-even are cost ÷ THIS yield, and the panel used
            // to print $/bu with the divisor nowhere on screen — so a yield keyed
            // once and never revisited read as a live number. Kennedy's wheat was
            // divided by a hand-entered 71 while the APH said 58 and the actual
            // 10-year history averaged ~63. Name the number, and say whether it is
            // an estimate or a harvested fact.
            const yInfo = yieldInfo(crop);
            // Machinery method + what the guard is holding back, so the exclusion is
            // visible rather than a number that quietly went missing.
            const machMethod = copMachMethod(mode, crop, items);
            const machEntered = mode === 'detailed' ? copVal(mode, crop, items, 'machinery') : 0;
            const machExcluded = machMethod === 'custom' ? machEntered : 0;
            const fieldOps = copFieldOpsTotal(mode, crop, items);
            // The OTHER mode keeps its own entries in the same bag and totals to its
            // own number. Only the active mode's keys sum, so the two modes can sit
            // $100s apart on one farm — surface it instead of letting a toggle move
            // the cost/acre with no explanation.
            const otherMode = mode === 'detailed' ? 'streamlined' : 'detailed';
            const otherKeyed = COP_CATEGORIES[otherMode].some(c => items[c.key] != null);
            const otherTotal = otherKeyed ? copSum(otherMode, crop, items) : null;
            // fwdMark = $/bu applied to UNPRICED bushels (forward-curve wtd avg, else futures ref).
            const fwdMark = fwdAvgPrice(crop) || getFutures(crop).price;
            const sold = soldBu(crop);                                   // contracted bushels (locked)
            const pricedRev = cropContracts(crop)
              .reduce((s, c) => s + cBushels(c) * netPrice(c), 0);       // locked revenue at contract prices
            const totBu = acres * yld;
            const pctPriced = totBu ? Math.min(100, sold / totBu * 100) : 0;
            // Blended effective price = locked contracts at their own prices + remaining
            // bushels at the forward curve, divided across expected production. This is the
            // price that makes GP/acre reflect your ACTUAL total-year result, not a pure
            // market mark. With nothing priced it collapses back to fwdMark.
            const blendMark = (acres && sold && totBu) ? (pricedRev + Math.max(0, totBu - sold) * fwdMark) / totBu : fwdMark;
            // Hedge-aware GP/acre at yield factor yf and cash-price factor pf: priced bushels
            // stay locked; only unpriced bushels move with the cash-price columns. Center cell
            // (1,1) reconciles exactly with the blended GP/acre headline.
            const gpAcreAt = (yf, pf) => {
              const px = fwdMark * pf;
              if (!acres || !sold) return yld ? yld * yf * px - total : null;   // nothing priced → whole crop at market
              const prod = acres * yld * yf;
              const unpriced = Math.max(0, prod - sold);
              return (pricedRev + unpriced * px - acres * total) / acres;
            };
            const gpAcre = gpAcreAt(1, 1);
            const totalGP = (acres && gpAcre != null) ? gpAcre * acres : null;
            const yieldSteps = [0.8, 0.9, 1.0, 1.1, 1.2];
            const priceSteps = [0.85, 0.925, 1.0, 1.075, 1.15];
            return (
              <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1a2e1a', fontFamily: "'Lora',serif" }}>Cost of Production</div>
                    <div style={{ fontSize: 11, color: '#60735f' }}>Builds the cost/acre that feeds the Crop Plan. Auto-saves per crop.</div>
                    <div style={{ fontSize: 10, color: '#677167', marginTop: 2 }}>Pre-filled with WNY/Northeast extension-budget estimates — override any line with your real numbers.</div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {GRAIN_CROPS.map(c => (
                        <button key={c} onClick={() => setCopCrop(c)} style={{ padding: '6px 12px', borderRadius: 7, fontSize: 12, fontWeight: copCrop === c ? 800 : 600, cursor: 'pointer', border: '1px solid', background: copCrop === c ? grainCropColor(c) : '#fff', color: copCrop === c ? '#fff' : '#4a6e4a', borderColor: copCrop === c ? grainCropColor(c) : '#c8d8c0', fontFamily: 'inherit' }}>{c}</button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', border: '1px solid #c8d8c0', borderRadius: 7, overflow: 'hidden' }}>
                      {['detailed', 'streamlined'].map(m => (
                        <button key={m} onClick={() => handleCOPMode(crop, m)} style={{ padding: '6px 12px', fontSize: 12, fontWeight: mode === m ? 800 : 600, cursor: 'pointer', border: 'none', background: mode === m ? '#2d4a2d' : '#fff', color: mode === m ? '#fff' : '#4a6e4a', fontFamily: 'inherit', textTransform: 'capitalize' }}>{m}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                  <div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead><tr>
                        <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 10, color: '#60735f', textTransform: 'uppercase', borderBottom: '1px solid #dde8d8' }}>Category</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: '#60735f', textTransform: 'uppercase', borderBottom: '1px solid #dde8d8', width: 46 }}>Passes</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: '#60735f', textTransform: 'uppercase', borderBottom: '1px solid #dde8d8' }}>Rate</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: '#60735f', textTransform: 'uppercase', borderBottom: '1px solid #dde8d8', width: 62 }}>$/acre</th>
                      </tr></thead>
                      <tbody>
                        {copGroups.map(g => (
                          <React.Fragment key={g.name || '_flat'}>
                            {g.name && (
                              <tr>
                                <td colSpan={4} style={{ padding: '10px 6px 3px', fontSize: 10, fontWeight: 800, color: '#2d6a1a', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{g.name}</td>
                              </tr>
                            )}
                            {g.items.map(cat => {
                              // Machinery ownership is struck out (not deleted) when the
                              // custom-rate method is in force — the figure stays visible
                              // and switching methods brings it straight back.
                              const machOff = cat.key === 'machinery' && machMethod === 'custom';
                              return (
                              <tr key={cat.key}>
                                <td style={{ padding: '3px 6px', color: machOff ? '#b3c0b0' : cat.credit ? '#2d6a1a' : '#4a6e4a', fontWeight: cat.credit ? 700 : 400, textDecoration: machOff ? 'line-through' : 'none' }}>
                                  {cat.credit ? '− ' : ''}{cat.label}
                                  {cat.hint && <span style={{ marginLeft: 6, fontSize: 10, color: '#b3c0b0', fontStyle: 'italic' }}>{cat.hint}</span>}
                                  {(cat.key === 'insurance' || cat.key === 's_insurance') && items[cat.key] == null && insurancePremiumPerAcre(crop) != null && (
                                    <span style={{ marginLeft: 6, fontSize: 10, color: '#2d6a1a', fontStyle: 'italic' }}>from policy</span>
                                  )}
                                  {machOff && <span style={{ marginLeft: 6, fontSize: 10, color: '#8a6a00', fontStyle: 'italic', textDecoration: 'none', display: 'inline-block' }}>not charged — passes are all-in</span>}
                                </td>
                                {/* Per-pass lines take a pass count; every other line spans
                                    the passes column so the table stays one grid. */}
                                {cat.perPass ? (
                                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                                    <input type="number" min="0" step="1" key={`${crop}-${mode}-${cat.key}-passes`} defaultValue={items[`${cat.key}__passes`] ?? 1} placeholder="1"
                                      onBlur={e => handleCOPChange(crop, `${cat.key}__passes`, e.target.value)}
                                      style={{ ...S.inputSm, width: 40, textAlign: 'right' }} />
                                  </td>
                                ) : <td />}
                                <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                                  <input type="number" min="0" step="1" key={`${crop}-${mode}-${cat.key}-${copDefault(mode, crop, cat.key) ?? ''}`} defaultValue={items[cat.key] ?? (copDefault(mode, crop, cat.key) ?? '')} placeholder="0"
                                    onBlur={e => handleCOPChange(crop, cat.key, e.target.value)}
                                    style={{ ...S.inputSm, width: 74, textAlign: 'right' }} />
                                </td>
                                <td style={{ padding: '3px 6px', textAlign: 'right', fontVariantNumeric: "tabular-nums", fontSize: 11, color: cat.perPass && copPasses(items, cat.key) !== 1 ? '#1a2e1a' : '#677167' }}>
                                  {gFmtD(copLine(mode, crop, items, cat))}
                                </td>
                              </tr>
                              );
                            })}
                            {g.name && (
                              <tr>
                                <td colSpan={3} style={{ padding: '2px 6px 6px', fontSize: 10, color: '#60735f', borderBottom: '1px solid #eef4ea' }}>{g.name} subtotal</td>
                                <td style={{ padding: '2px 6px 6px', textAlign: 'right', fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: groupSum(g) < 0 ? '#2d6a1a' : '#4a6e4a', borderBottom: '1px solid #eef4ea' }}>{gFmtD(groupSum(g))}</td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                      <tfoot><tr style={{ background: '#f0f6ec', borderTop: '2px solid #2d4a2d' }}>
                        <td colSpan={3} style={{ padding: '7px 6px', fontWeight: 800, color: '#1a2e1a' }}>Total cost / acre</td>
                        <td style={{ padding: '7px 6px', textAlign: 'right', fontWeight: 800, fontVariantNumeric: "tabular-nums", fontSize: 15, color: '#1a2e1a' }}>{gFmtD(total)}</td>
                      </tr></tfoot>
                    </table>
                    <div style={{ fontSize: 10, color: '#677167', marginTop: 6 }}>
                      Auto-saves and feeds the Crop Plan cost/acre for {crop}.
                      {copSave?.status === 'saving' && <span style={{ marginLeft: 6, color: '#60735f' }}>saving…</span>}
                      {copSave?.status === 'ok' && <span style={{ marginLeft: 6, color: '#2d6a1a' }}>✓ saved</span>}
                      {copSave?.status === 'err' && <span style={{ marginLeft: 6, color: '#c0392b', fontWeight: 700 }}>⚠ NOT saved — this screen and the Crop Plan are out of step. Retry an entry.</span>}
                    </div>
                    {/* Machinery accounting method — a choice, not a footnote. The two
                        methods are mutually exclusive and picking one is what stops
                        ownership being charged twice. */}
                    {mode === 'detailed' && (
                      <div style={{ marginTop: 8, border: '1px solid #e4ece4', borderRadius: 8, background: '#f9fcf6', padding: '8px 10px' }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#4a6e4a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Machinery accounting</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                          {[['custom', 'Custom rates (all-in passes)'], ['owned', 'Owned (ownership on its own line)']].map(([m, lbl]) => (
                            <button key={m} onClick={() => handleCOPMachMethod(crop, m)}
                              style={{ padding: '5px 10px', borderRadius: 7, fontSize: 11, fontWeight: machMethod === m ? 800 : 600, cursor: 'pointer', border: '1px solid', background: machMethod === m ? '#2d4a2d' : '#fff', color: machMethod === m ? '#fff' : '#4a6e4a', borderColor: machMethod === m ? '#2d4a2d' : '#c8d8c0', fontFamily: 'inherit' }}>{lbl}</button>
                          ))}
                        </div>
                        <div style={{ fontSize: 10, color: '#60735f', lineHeight: 1.45 }}>
                          {machMethod === 'custom'
                            ? <>Each Field Operations pass is an all-in custom rate — fuel, repairs, labor <b>and</b> depreciation are already inside it ({gFmtD(fieldOps)}/ac across all passes). The <i>Machinery — deprec. &amp; interest</i> line is therefore <b>not charged</b>.</>
                            : <>Field Operations are operating cost only (fuel + repairs + labor) and ownership is carried on the <i>Machinery — deprec. &amp; interest</i> line.</>}
                        </div>
                        {machExcluded > 0 && (
                          <div style={{ fontSize: 10.5, color: '#8a6a00', marginTop: 6, fontWeight: 600 }}>
                            {gFmtD(machExcluded)}/ac of machinery ownership excluded — it would have been charged twice on top of {gFmtD(fieldOps)}/ac of all-in passes. Switch to <b>Owned</b> if your pass rates are operating-cost only.
                          </div>
                        )}
                      </div>
                    )}
                    {mode === 'detailed' && (
                      <div style={{ fontSize: 10, color: '#677167', marginTop: 6, lineHeight: 1.4 }}>
                        <b style={{ color: '#60735f' }}>Field Operations</b> are a rate per pass × a pass count — set tillage to 0 passes for no-till, spraying to 3 for a three-pass program.
                      </div>
                    )}
                    {otherTotal != null && (
                      <div style={{ fontSize: 10, color: '#8a6a00', marginTop: 6, lineHeight: 1.4 }}>
                        This crop also has <b>{otherMode}</b> entries saved, which total <b>{gFmtD(otherTotal)}/ac</b>. Only the mode shown here counts — switching the toggle changes the cost/acre the Crop Plan uses.
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                      {[
                        ['Cost / acre', gFmtD(total), '#1a2e1a', machExcluded > 0 ? `machinery ownership excluded (${gFmtD(machExcluded)})` : null],
                        ['Cost / bushel', costPerBu != null ? gFmtD(costPerBu) : '—', '#1a2e1a', yld ? `÷ ${yld} bu/ac` : 'set a yield on the Crop Plan'],
                        ['Break-even price', costPerBu != null ? gFmtD(costPerBu) : '—', '#8a6a00', yld ? <YieldBasisChip info={yInfo} /> : null],
                        [`GP/acre @ ${gFmtD(blendMark)}${pctPriced > 0 ? ' blended' : ''}`, gpAcre != null ? gFmtD(gpAcre) : '—', gpColor(gpAcre || 0), null],
                      ].map(([lbl, val, col, sub]) => (
                        <div key={lbl} style={{ background: '#f9fcf6', border: '1px solid #e4ece4', borderRadius: 8, padding: '8px 10px' }}>
                          <div style={{ fontSize: 10, color: '#60735f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lbl}</div>
                          <div style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: col }}>{val}</div>
                          {sub && <div style={{ fontSize: 10, color: '#677167', marginTop: 1, lineHeight: 1.3 }}>{sub}</div>}
                        </div>
                      ))}
                    </div>
                    {totalGP != null && (
                      <div style={{ background: '#f9fcf6', border: '1px solid #e4ece4', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
                        <div style={{ fontSize: 10, color: '#60735f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Projected {crop} P/L · {Math.round(acres)} ac</div>
                        <div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: gpColor(totalGP) }}>{gFmtD(totalGP)}</div>
                        <div style={{ fontSize: 10, color: '#677167', marginTop: 2 }}>
                          {pctPriced > 0
                            ? `${pctPriced.toFixed(0)}% priced at locked prices + ${(100 - pctPriced).toFixed(0)}% unsold @ ${gFmtD(fwdMark)} fwd curve`
                            : `All ${Math.round(totBu).toLocaleString()} bu unsold @ ${gFmtD(fwdMark)} fwd curve`}
                          {cropContracts(crop).some(htaBasisUnfixed) && ' · incl. placeholder basis on unfixed HTAs'}
                        </div>
                      </div>
                    )}
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#4a6e4a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Break-even sensitivity · GP / acre</div>
                    {yld > 0 && total > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                        <thead><tr>
                          <th style={{ padding: '3px', fontSize: 9, color: '#60735f', textAlign: 'left' }}>yld ↓ / price →</th>
                          {priceSteps.map(ps => <th key={ps} style={{ padding: '3px', textAlign: 'center', fontVariantNumeric: "tabular-nums", color: '#60735f' }}>{gFmtD(fwdMark * ps)}</th>)}
                        </tr></thead>
                        <tbody>
                          {yieldSteps.map(ys => {
                            const y = yld * ys;
                            return (
                              <tr key={ys}>
                                <td style={{ padding: '3px', fontVariantNumeric: "tabular-nums", color: '#60735f', whiteSpace: 'nowrap' }}>{Math.round(y)} bu</td>
                                {priceSteps.map(ps => {
                                  const gp = gpAcreAt(ys, ps) || 0;
                                  const bg = gp >= 0 ? `rgba(58,110,50,${Math.min(0.5, gp / (total || 1) * 0.4 + 0.08).toFixed(3)})` : `rgba(192,57,43,${Math.min(0.5, -gp / (total || 1) * 0.4 + 0.08).toFixed(3)})`;
                                  return <td key={ps} style={{ padding: '4px 3px', textAlign: 'center', fontVariantNumeric: "tabular-nums", background: bg, color: '#1a2e1a', fontWeight: 600 }}>{Math.round(gp)}</td>;
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : <div style={{ fontSize: 11, color: '#aaa' }}>Needs a yield (Crop Plan — or keyed crop-insurance APH) plus costs above to show the sensitivity grid.</div>}
                    <div style={{ fontSize: 10, color: '#677167', marginTop: 6 }}>Rows = yield (bu/ac), columns = cash price on <b>unsold</b> bushels. Cell = gross profit/acre (revenue − cost).{pctPriced > 0 ? ` Your ${pctPriced.toFixed(0)}% priced bushels stay at locked prices, so only unsold bushels move with the columns.` : ''} Green = profit, red = loss.</div>
                  </div>
                </div>
              </div>
            );
          })()
        )}

        {tab === 'import' && <div>
          {/* One import home — two clearly-labeled sources. Both write to shared state, so
              contracts AND scale tickets flow to both the Tool and the Marketing Plan. */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 16, alignItems: 'start' }}>
            <div style={{ ...S.card, marginBottom: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#1a2e1a', marginBottom: 3 }}>📋 Import contracts</div>
              <div style={{ fontSize: 10.5, color: '#60735f', marginBottom: 10, lineHeight: 1.45 }}>Bushel contract export — one row per pricing line. Existing lines update, new insert, and lines no longer in the file are removed. Feeds the Tool and the Plan's % sold.</div>
              <div style={{ border: '2px dashed #c8d8c0', borderRadius: 8, background: '#fafcf8', textAlign: 'center', padding: '22px 16px', cursor: 'pointer' }}
            onClick={() => document.getElementById('grain-csv-input').click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleCSVFile(e.dataTransfer.files[0]); }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⬆</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2e1a', marginBottom: 6 }}>Drop CSV here or click to browse</div>
            <div style={{ fontSize: 11, color: '#60735f' }}>
              Drag a Schofield-style export (one row per pricing line) or use the simple template below.<br />
              Upload your full Bushel dump — existing lines update, new lines insert, and lines no longer in the file are removed.
            </div>
            <input type="file" id="grain-csv-input" accept=".csv" style={{ display: 'none' }} onChange={e => handleCSVFile(e.target.files[0])} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button style={S.btnSmOut} onClick={() => {
              const csv = 'contract_number,commodity,type,total_contracted,total_priced,total_delivered,total_unpriced,date_created,pricing_line_id,delivery_start,delivery_end,location_pricing_line,futures_reference,futures_price,basis,cash_price,quantity_priced,quantity_delivered\n' +
                '"100884","Corn","Fixed Price","17671","17671","17671","0","06/02/2025","100884-P00","06/01/2025","07/01/2025","Picked Up","ZCN25","4.41","0.04","4.45","0","0"\n' +
                '"100884","Corn","Fixed Price","17671","17671","17671","0","06/02/2025","100884-P01","06/01/2025","07/01/2025","Picked Up","ZCN25","4.41","0.04","4.45","17671","17671"\n' +
                '"102200","Corn","Basis Only","40000","40000","40000","0","10/15/2025","102200-P00","11/01/2025","01/01/2026","Schofield Farms","ZCH26","","0.65","4.88","0","0"\n' +
                '"102200","Corn","Basis Only","40000","40000","40000","0","10/15/2025","102200-P02","11/01/2025","12/01/2025","Schofield Farms","ZCZ25","","0.65","4.99","20000","20000"';
              const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv); a.download = 'grain_template.csv'; a.click();
            }}>⬇ Download Template</button>
            <button style={S.btnSmOut} onClick={handleRecalcCropYears} disabled={saving}
              title="Re-derive crop_year for every contract on this farmer using delivery date / futures month. Useful if contracts were imported with the wrong year.">
              {saving ? '…' : '🔁 Recalculate Crop Years'}
            </button>
          </div>
            </div>

            {/* ── Import scale tickets (Bushel scale-ticket export) ── */}
            <div style={{ ...S.card, marginBottom: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#1a2e1a', marginBottom: 3 }}>🎫 Import scale tickets</div>
              <div style={{ fontSize: 10.5, color: '#60735f', marginBottom: 10, lineHeight: 1.45 }}>Bushel scale-ticket export — all crops. Each load is attributed to a crop-year, fills its contracts, and surfaces open storage. Re-uploading only brings in new loads. Feeds the Tool and the Plan.</div>
              <div style={{ border: '2px dashed #c8d8c0', borderRadius: 8, background: '#fafcf8', textAlign: 'center', padding: '22px 16px', cursor: 'pointer' }} onClick={() => document.getElementById('grain-ticket-input').click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onImportTickets(f); }}>
                <div style={{ fontSize: 26, marginBottom: 6 }}>⬆</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2e1a' }}>Drop scale-ticket file or click to browse</div>
                <div style={{ fontSize: 10, color: '#677167', marginTop: 3 }}>.csv · .xlsx · .xls</div>
                <input type="file" id="grain-ticket-input" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) onImportTickets(f); e.target.value = ''; }} />
              </div>
              {ticketImport ? <div style={{ fontSize: 11, marginTop: 10, fontWeight: 600, color: ticketImport.status === 'err' ? '#c0392b' : ticketImport.status === 'ok' ? '#1f7a3d' : '#677167' }}>{ticketImport.msg}</div> : null}
            </div>
          </div>

          {importPreview && <div style={S.card}>
            {importPreview.length === 0 ? (
              <div style={{ background: '#fdecea', border: '1px solid #e0a0a0', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#a32020', fontWeight: 700, marginBottom: 4 }}>
                ⚠ Couldn't read any contracts from this file. Make sure it's the <b>contracts</b> export — the one with columns like <code>contract_number</code>, <code>commodity</code>, <code>pricing_line_id</code> — and not the scale-ticket file.
              </div>
            ) : (
              <div style={{ background: '#fff4ec', border: '1px solid #e8c89a', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#7a4a00', fontWeight: 700, marginBottom: 12 }}>
                ⚠ Not saved yet — review below, then click the green <b>Import</b> button at the bottom. Nothing is written until you do.
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 800, color: '#4a6e4a', marginBottom: 4 }}>Preview — {importPreview.length} pricing line{importPreview.length === 1 ? '' : 's'}</div>
            <div style={{ fontSize: 11, color: '#60735f', marginBottom: 12 }}>
              {(() => {
                const uniqueContracts = new Set(importPreview.map(r => r.contract_number).filter(Boolean));
                return `${uniqueContracts.size} unique contract${uniqueContracts.size === 1 ? '' : 's'} across these lines`;
              })()}
            </div>
            {/* WHO BOUGHT IT. The export has no buyer column — its `account` column
                is the SELLER — so this is asked once and written to every row. The
                account name is printed beside it, labelled, so the two can never be
                confused again the way they were on 103961/104502. */}
            {importPreview.length > 0 && (() => {
              const accounts = [...new Set(importPreview.map(r => (r.account || '').trim()).filter(Boolean))];
              const known = [...new Set(contracts.map(c => (c.buyer || '').trim()).filter(Boolean))];
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 12px', background: '#f2f6f2', borderRadius: 6, border: '1px solid #d4e0cf' }}>
                  <label style={{ fontSize: 11, fontWeight: 800, color: '#2d4a2d' }}>Buyer (elevator this export came from)</label>
                  <input list="grain-import-buyers" value={importBuyer.value} placeholder="e.g. Howlett Farms"
                    onChange={e => setImportBuyer({ value: e.target.value, from: 'typed' })}
                    style={{ padding: '5px 9px', border: '1px solid #c8d8c0', borderRadius: 5, fontSize: 12, minWidth: 190, background: '#fff' }} />
                  <datalist id="grain-import-buyers">{known.map(b => <option key={b} value={b} />)}</datalist>
                  {importBuyer.from === 'file' && <span style={{ fontSize: 10.5, color: '#1f7a3d', fontWeight: 700 }}>✓ named in the file</span>}
                  {importBuyer.from === 'book' && <span style={{ fontSize: 10.5, color: '#7a4a1a', fontWeight: 700 }}>from this farm's existing contracts — confirm it</span>}
                  {!importBuyer.value.trim() && importBuyer.from !== 'book-conflict' && <span style={{ fontSize: 10.5, color: '#a32020', fontWeight: 700 }}>required — the file doesn't say</span>}
                  {/* THE ELEVATOR'S OWN SITE NAME, ALWAYS ON SCREEN. Every row of
                      the Edgewood file stored location "EDGEWOOD" while the buyer
                      field said "Howlett Farms"; the contradiction was in the data
                      and nowhere on the page. */}
                  {(importBuyer.locations || []).length > 0 && (
                    <span style={{ fontSize: 10.5, color: '#60735f' }}>
                      Location in this file: <b style={{ color: '#4a6e4a' }}>{importBuyer.locations.slice(0, 4).join(', ')}{importBuyer.locations.length > 4 ? '…' : ''}</b>
                    </span>
                  )}
                  {importBuyer.from === 'book-conflict' && (
                    <div style={{ width: '100%', marginTop: 4, padding: '7px 9px', background: '#fff4ec', border: '1px solid #e8c89a', borderRadius: 4, fontSize: 11, color: '#7a4a00', fontWeight: 700 }}>
                      ⚠ This looks like a DIFFERENT elevator. This farm's imported contracts are all <b>{importBuyer.rejected}</b>, but this file's locations say <b>{(importBuyer.locations || []).slice(0, 4).join(', ')}</b> — so nothing was filled in. Type who actually bought this grain.
                      <div style={{ fontWeight: 400, marginTop: 3 }}>
                        Getting it wrong files one elevator's contracts under another's name, and the buyer is what decides which rows a later import may remove.
                      </div>
                    </div>
                  )}
                  {accounts.length > 0 && (
                    <span style={{ fontSize: 10.5, color: '#60735f', marginLeft: 'auto' }}>
                      Seller on this file: <b style={{ color: '#4a6e4a' }}>{accounts.join(', ')}</b> — the elevator's account name for this farm, not the buyer
                    </span>
                  )}
                </div>
              );
            })()}
            {/* WHAT THIS PARSER TOOK OFF THE FILE, ON SCREEN NEXT TO THE ROWS.
                An elevator that prices by ROLLING units into a child contract
                ships both sides in one export, so a straight read of Edgewood's
                corn file totalled 300,000 bu against the elevator's 150,000.
                Netting it silently would be its own defect — a bushel that
                disappears between the file and the book has to be accounted for
                where somebody can see it, or the next reader has no way to tell
                a correct import from a lossy one. */}
            {importPreview.length > 0 && (() => {
              const rolled     = importPreview.filter(r => (r._rolledOutBu || 0) > 0);
              const unresolved = importPreview.filter(r => (r._rollsUnresolved || []).length > 0);
              const overran    = importPreview.filter(r => r._rollOverran);
              const qpIgnored  = importPreview.filter(r => (r._quantityPricedIgnored || 0) > 0);
              if (!rolled.length && !unresolved.length && !overran.length && !qpIgnored.length) return null;
              const rolledBu = rolled.reduce((s, r) => s + r._rolledOutBu, 0);
              const qpBu     = qpIgnored.reduce((s, r) => s + r._quantityPricedIgnored, 0);
              return (
                <div style={{ marginBottom: 14, padding: '10px 12px', background: '#f2f6f2', borderRadius: 6, border: '1px solid #d4e0cf', fontSize: 11.5, color: '#2d4a2d' }}>
                  <div style={{ fontWeight: 800, marginBottom: 6, color: '#4a6e4a' }}>What this file says twice</div>
                  {rolled.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      ↪ <b>{rolled.length}</b> contract{rolled.length === 1 ? '' : 's'} rolled <b>{Math.round(rolledBu).toLocaleString()} bu</b> into child contracts that are also in this file, so those bushels are counted once — on the child.
                      <div style={{ color: '#60735f', marginTop: 3 }}>
                        {rolled.map(r => `${r.contract_number} → ${r._rollTargets.join(', ')} (${Math.round(r._rolledOutBu).toLocaleString()} bu)`).join(' · ')}
                      </div>
                    </div>
                  )}
                  {qpIgnored.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      ○ <b>{Math.round(qpBu).toLocaleString()} bu</b> on {qpIgnored.length} line{qpIgnored.length === 1 ? '' : 's'} read as <b>unpriced</b> — the line carries a quantity but states no futures or cash price, and the contract's own <code>Total_Priced</code> is 0.
                    </div>
                  )}
                  {unresolved.length > 0 && (
                    <div style={{ marginTop: 6, padding: '6px 8px', background: '#fff4ec', border: '1px solid #e8c89a', borderRadius: 4, color: '#7a4a00', fontWeight: 700 }}>
                      ⚠ {unresolved.length} contract{unresolved.length === 1 ? '' : 's'} name a roll into a contract that is <b>not in this file</b> — those bushels were <b>kept in full</b> rather than removed. Export the other commodities/dates too if the child belongs here.
                      <div style={{ fontWeight: 400, marginTop: 3 }}>
                        {unresolved.map(r => `${r.contract_number} → ${r._rollsUnresolved.map(x => x.target).join(', ')}`).join(' · ')}
                      </div>
                    </div>
                  )}
                  {overran.length > 0 && (
                    <div style={{ marginTop: 6, padding: '6px 8px', background: '#fdecea', border: '1px solid #e0a0a0', borderRadius: 4, color: '#a32020', fontWeight: 700 }}>
                      ⚠ {overran.length} contract{overran.length === 1 ? '' : 's'} claim a roll LARGER than the contract holds. Nothing was netted there — check {overran.map(r => r.contract_number).join(', ')} against the elevator before importing.
                    </div>
                  )}
                </div>
              );
            })()}
            {importDelta && (() => {
              // Recomputed on every render off the CURRENT buyer field, because
              // that field decides which rows are removable and it is editable
              // right here. A count captured when the file was read would drift
              // from what the confirm actually deletes.
              const sweep = grainImportStaleIds(importDelta.existingRows, importPreview, importBuyer.value);
              const staleCount = sweep.staleIds.length;
              const kept = sweep.keptOtherBuyer;
              if (!(importDelta.updateCount > 0 || importDelta.insertCount > 0 || (importDelta.protectedCount || 0) > 0 || staleCount > 0 || kept.length > 0)) return null;
              const bigSweep = staleCount > 0 && staleCount >= importDelta.updateCount + importDelta.insertCount;
              const keptBuyers = [...new Set(kept.map(r => (r.buyer || '').trim() || 'no buyer recorded'))];
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 11, marginBottom: 14, padding: '8px 12px', background: '#f2f6f2', borderRadius: 6, border: '1px solid #d4e0cf' }}>
                  <span style={{ color: '#1a5a1a', fontWeight: 700 }}>
                    ↻ Update <span style={{ fontVariantNumeric: "tabular-nums" }}>{importDelta.updateCount}</span>
                    <span style={{ color: '#60735f', fontWeight: 400, marginLeft: 4 }}>existing</span>
                  </span>
                  <span style={{ color: '#2d4a2d', fontWeight: 700 }}>
                    + Insert <span style={{ fontVariantNumeric: "tabular-nums" }}>{importDelta.insertCount}</span>
                    <span style={{ color: '#60735f', fontWeight: 400, marginLeft: 4 }}>new</span>
                  </span>
                  {(importDelta.protectedCount || 0) > 0 && (
                    <span style={{ color: '#1a4a7a', fontWeight: 700 }} title="Hand-edited contracts (✏️ edited) are farmer-owned and left untouched by the import.">
                      ✏️ Keep <span style={{ fontVariantNumeric: "tabular-nums" }}>{importDelta.protectedCount}</span>
                      <span style={{ color: '#60735f', fontWeight: 400, marginLeft: 4 }}>edited</span>
                    </span>
                  )}
                  {staleCount > 0 && (
                    <span style={{ color: bigSweep ? '#a32020' : '#7a4a1a', fontWeight: 700 }}>
                      × Remove <span style={{ fontVariantNumeric: "tabular-nums" }}>{staleCount}</span>
                      <span style={{ color: '#60735f', fontWeight: 400, marginLeft: 4 }}>no longer in file</span>
                    </span>
                  )}
                  {kept.length > 0 && (
                    <span style={{ color: '#1a4a7a', fontWeight: 700 }} title="An export speaks only for its own elevator. Rows bought by someone else — or with no buyer recorded — are never removed by it.">
                      🛡 Keep <span style={{ fontVariantNumeric: "tabular-nums" }}>{kept.length}</span>
                      <span style={{ color: '#60735f', fontWeight: 400, marginLeft: 4 }}>from {keptBuyers.join(', ')}</span>
                    </span>
                  )}
                  <span style={{ color: '#60735f', marginLeft: 'auto', fontStyle: 'italic' }}>this export is the source of truth for {importBuyer.value.trim() || 'its own elevator'}</span>
                  {bigSweep && (
                    <div style={{ width: '100%', marginTop: 6, padding: '6px 8px', background: '#fff4ec', border: '1px solid #e8c89a', borderRadius: 4, color: '#a32020', fontWeight: 700 }}>
                      ⚠ This upload would remove more contracts than it keeps. Confirm this is a full dump for this elevator and not a partial export before applying.
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ overflowX: 'auto', marginBottom: 14 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr>{['Contract #', 'Line', 'Crop', 'Delivery', 'Type', 'Bushels', 'Futures', 'Basis', 'Cash'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>{importPreview.slice(0, 12).map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{r.contract_number || '—'}</td>
                    <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", color: '#60735f' }}>{r.pricing_line_id ? r.pricing_line_id.replace(/^.*-/, '') : '—'}</td>
                    <td style={{ ...S.td, color: grainCropColor(r.crop) || '#1a2e1a', fontWeight: 600 }}>{r.crop}{grainProgramChip(r.crop_program)}</td>
                    <td style={S.td}><span style={S.typeBadge(r.contract_type)}>{r.contract_type}</span></td>
                    <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right' }}>{r.bushels_contracted != null ? Number(r.bushels_contracted).toLocaleString() : '—'}</td>
                    <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right' }}>{r.futures_price != null ? gFmtD(r.futures_price) : '—'}</td>
                    <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right' }}>{r.basis != null ? Number(r.basis).toFixed(2) : '—'}</td>
                    <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", textAlign: 'right', fontWeight: 700 }}>{r.cash_price != null ? gFmtD(r.cash_price) : '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {importPreview.length > 12 && <div style={{ fontSize: 11, color: '#60735f', marginBottom: 10 }}>...and {importPreview.length - 12} more rows</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...S.btnGreen, opacity: (saving || !importBuyer.value.trim()) ? 0.6 : 1 }} onClick={confirmImport}
                disabled={saving || !importBuyer.value.trim()}
                title={!importBuyer.value.trim() ? 'Enter the buyer (elevator) this export came from first.' : undefined}>
                {saving
                  ? 'Importing…'
                  : !importBuyer.value.trim()
                    ? 'Enter the buyer to import'
                    : importDelta
                      ? (() => {
                          const n = grainImportStaleIds(importDelta.existingRows, importPreview, importBuyer.value).staleIds.length;
                          return `Apply (${importDelta.updateCount} update, ${importDelta.insertCount} new${n > 0 ? `, ${n} remove` : ''})`;
                        })()
                      : `Import ${importPreview.length} contracts`}
              </button>
              <button style={S.btnOut} onClick={() => { setImportPreview(null); setImportDelta(null); setImportBuyer({ value: '', from: null }); }}>Cancel</button>
            </div>
          </div>}
        </div>}
      </>}

      {modal === 'contract' && <GrainContractModal form={contractForm} editing={!!editingContract} saving={saving} getFutures={getFutures} cropYear={cropYear}
        // The delivery points this farm actually uses, off its own contracts —
        // typed free-hand, "Star of the West" and "star of west" become two
        // delivery points and neither matches a posted basis column.
        knownLocations={[...new Set(contracts.map(c => (c.location || '').trim()).filter(Boolean))].sort()}
        onClose={() => { setModal(null); setEditingContract(null); }} onSave={handleSaveContract} onDelete={editingContract ? () => handleDeleteContract(editingContract.id) : null} />}
      {modal === 'futures' && <GrainFuturesModal futuresRef={futuresRef} cropYear={cropYear} saving={saving} onClose={() => setModal(null)} onSave={handleSaveFutures} />}
    </div>
  );

  function openFuturesModal() { setModal('futures'); }
}

// Multi-segment donut chart. Each segment is a colored arc representing
// one slice of the data — used here to show contract-type mix on a
// crop card. When `cap` is provided, the unfilled portion stays gray
// (so segments + gray = a "% sold of total plan" view).
//
// Props:
//   segments: [{ value: number, color: string, label?: string }]
//   cap:      total to fill against (e.g. totalBu for "% of plan"). If
//             omitted or <= sum(segments), the donut auto-fills.
//   centerLabel / centerSub: text in the middle
//   size:     pixel width/height
function ContractTypeDonut({ segments, cap, centerLabel, centerSub, size = 96 }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  const denom = (cap && cap > total) ? cap : total;
  const stroke = Math.max(10, size * 0.18);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2, cy = size / 2;
  let cumulative = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f4f0" strokeWidth={stroke} />
      {denom > 0 && segments.map((seg, i) => {
        const value = seg.value || 0;
        if (value <= 0) return null;
        const dash = (value / denom) * c;
        const offset = (cumulative / denom) * c;
        cumulative += value;
        return (
          <circle
            key={i}
            cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${c}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
      {centerLabel && (
        <text x={cx} y={cy - (centerSub ? size * 0.05 : 0)} textAnchor="middle" dominantBaseline="central"
          style={{ fontSize: size * 0.22, fontWeight: 800, fill: '#1a2e1a', fontFamily: 'inherit' }}>
          {centerLabel}
        </text>
      )}
      {centerSub && (
        <text x={cx} y={cy + size * 0.14} textAnchor="middle" dominantBaseline="central"
          style={{ fontSize: size * 0.1, fontWeight: 600, fill: '#60735f', fontFamily: 'inherit' }}>
          {centerSub}
        </text>
      )}
    </svg>
  );
}

// Farmer-selector landing page for /grain-marketing.
// Dedupes the upstream `farmers` array by normalized name (the upstream
// has one entry per farmer×policy_number, so any farmer with NAU + R&H
// policies appears multiple times). Adds a search box and richer card
// content (counties, crop count) so the user can find a farmer quickly
// in a 200+ farmer book.
function GrainFarmerSelector({ farmerList, cropYear, isMobile, onSelect, cardStyle }) {
  const [search, setSearch] = useState('');
  const [contractCounts, setContractCounts] = useState({});

  // Dedupe by normalized name. Within each name group, prefer:
  //   1. The entry with the most policies (most complete record)
  //   2. The 'crop' policy_type (canonical for grain marketing)
  const uniqueFarmers = useMemo(() => {
    const byName = new Map();
    for (const f of farmerList) {
      const key = (f.name || '').trim().toLowerCase();
      if (!key) continue;
      const existing = byName.get(key);
      if (!existing) { byName.set(key, f); continue; }
      const fIsCrop = (f.policyType || '').toLowerCase() === 'crop';
      const eIsCrop = (existing.policyType || '').toLowerCase() === 'crop';
      const fPolicies = (f.allFarmerDbIds?.length || 0) + (f._allPolicyNums?.length || 0);
      const ePolicies = (existing.allFarmerDbIds?.length || 0) + (existing._allPolicyNums?.length || 0);
      if (fIsCrop && !eIsCrop) byName.set(key, f);
      else if (fIsCrop === eIsCrop && fPolicies > ePolicies) byName.set(key, f);
    }
    return Array.from(byName.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [farmerList]);

  // Load contract counts per farmer once on mount, so each card can show
  // "3 contracts" / "no contracts yet" — quick visual signal of activity.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const farmerIds = uniqueFarmers
          .flatMap(f => f.allFarmerDbIds || [f.id].filter(Boolean));
        if (!farmerIds.length) return;
        const idFilter = farmerIds.join(',');
        const rows = await sbFetch('grain_contracts', {
          filters: [`farmer_id=in.(${idFilter})`],
          select: 'farmer_id',
        }).catch(() => []);
        if (cancelled) return;
        const counts = {};
        for (const r of (rows || [])) counts[r.farmer_id] = (counts[r.farmer_id] || 0) + 1;
        setContractCounts(counts);
      } catch {/* table may not exist yet — silent */}
    }
    load();
    return () => { cancelled = true; };
  }, [uniqueFarmers]);

  // ── % sold per farm, for the little bars under each name ────────────────
  // Kevin, 2026-08-21: the Grain Book View page is gone; the one number it was
  // really consulted for — how far along is this farm — belongs here, on the
  // list you already open.
  //
  // ⚠ THIS FETCH MUST MIRROR THE FARM CARD'S, and the first cut of it did not.
  // Kevin, same day: "Meisenzahl % sold doesnt match his actual percent sold,
  // thats using stale data." Three divergences, every one of which renders a
  // confident wrong percentage rather than a blank:
  //
  //   1. POLICIES WERE NOT SCOPED TO THE CROP YEAR. crop_coverage hangs off a
  //      policy and carries no year of its own, so an unfiltered policy read
  //      fed CY2025 (and CY2027) coverage into a CY2026 position. That is
  //      exactly the "stale data" Kevin saw — old acres in the denominator.
  //   2. THE CHOPPED-ACRE HAIRCUT WAS DROPPED. planRows never reached
  //      buildGrainPosition, so a farm chopping half its corn read at its FULL
  //      policy guarantee here and at the haircut figure on its own page. That
  //      is the 2026-08-20 book-view defect — "two screens, one farm, two
  //      ceilings" — reintroduced by the commit that removed the book view.
  //   3. IT GROUPED BY RAW farmer_id, NOT BY FARM. A farmer split across
  //      several rows (see normFarmerName / the CY2027 rollover duplicates)
  //      would compute one partial position PER ROW and draw two "Corn" bars,
  //      neither matching the card. The card aggregates over allFarmerDbIds; so
  //      does this now.
  //
  // Still deliberately lighter than the page it replaces: only farms that HAVE
  // contracts, and only coverage under their own crop-year policies. Grain Book
  // View read every crop_coverage row in the database unfiltered, plus futures
  // and basis per crop. Committed and expected come from buildGrainPosition —
  // the same model the farm Summary uses, so the bar and the card cannot
  // disagree.
  const farmKey = (f) => ((f.allFarmerDbIds?.length ? f.allFarmerDbIds : [f.id].filter(Boolean)).slice().sort().join(','));
  const [soldByFarmer, setSoldByFarmer] = useState({});
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        if (!cropYear) { setSoldByFarmer({}); return; }
        // Only farms with contracts — a farm with none has no % to show.
        const groups = uniqueFarmers
          .map((f) => ({ key: farmKey(f), ownerIds: (f.allFarmerDbIds?.length ? f.allFarmerDbIds : [f.id].filter(Boolean)) }))
          .filter((g) => g.ownerIds.length && g.ownerIds.some((id) => (contractCounts[id] || 0) > 0));
        if (!groups.length) { setSoldByFarmer({}); return; }
        const allIds = Array.from(new Set(groups.flatMap((g) => g.ownerIds)));
        const idFilter = allIds.join(',');
        // Same reads, same selects, same crop-year scoping as the farm card.
        const [pols, contracts, plans] = await Promise.all([
          sbFetch('policies', { select: 'id,farmer_id', filters: [`farmer_id=in.(${idFilter})`, `crop_year=eq.${cropYear}`] }).catch(() => []),
          sbFetch('grain_contracts', { filters: [`farmer_id=in.(${idFilter})`, `crop_year=eq.${cropYear}`] }).catch(() => []),
          sbFetch('grain_crop_plan', { select: 'farmer_id,crop,chopped_acres', filters: [`farmer_id=in.(${idFilter})`, `crop_year=eq.${cropYear}`] }).catch(() => []),
        ]);
        if (cancelled) return;
        const polIds = (pols || []).map((p) => p.id);
        const coverage = polIds.length
          ? await sbFetch('crop_coverage', {
              // policy_id rides along so a row maps back to its farmer; the farm
              // card fetches per-farmer and does not need it. Everything else is the
              // SAME select, so buildGrainPosition sees identical rows.
              select: 'policy_id,county,crop,type,type_code,practice_code,plan,aph_yield,coverage_level,acres,pp_acres,liability,price_election,price_election_pct',
              filters: [`policy_id=in.(${polIds.join(',')})`],
            }).catch(() => [])
          : [];
        if (cancelled) return;
        const farmerByPolicy = new Map((pols || []).map((p) => [p.id, p.farmer_id]));
        const bucket = (rows, keyOf) => {
          const m = new Map();
          for (const r of rows || []) {
            const fid = keyOf(r);
            if (!fid) continue;
            if (!m.has(fid)) m.set(fid, []);
            m.get(fid).push(r);
          }
          return m;
        };
        const covByFarmer = bucket(coverage, (c) => farmerByPolicy.get(c.policy_id));
        const conByFarmer = bucket(contracts, (c) => c.farmer_id);
        const planByFarmer = bucket(plans, (p) => p.farmer_id);
        const out = {};
        for (const g of groups) {
          const cov = g.ownerIds.flatMap((id) => covByFarmer.get(id) || []);
          const con = g.ownerIds.flatMap((id) => conByFarmer.get(id) || []);
          const planRows = g.ownerIds.flatMap((id) => planByFarmer.get(id) || []);
          if (!con.length) continue;
          const crops = [];
          for (const crop of GRAIN_CROPS) {
            let pos;
            // One bad farm/crop must never blank the whole list.
            try {
              pos = buildGrainPosition({
                farm: '', crop: crop.toUpperCase(), cropYear,
                coverageRows: cov, contractRows: con,
                choppedAcres: choppedAcresFor(planRows, crop),
              });
            } catch { continue; }
            const expected = pos?.exposure?.expectedBu;
            const committed = pos?.currentProtection?.contractedBu;
            // The MODEL's own percentage, never a second division here. Same
            // field the farm card's banner prints, so the two cannot drift.
            const pct = pos?.currentProtection?.pctContracted;
            if (!(expected > 0) || !(committed > 0) || pct == null) continue;
            // AN INCOMPLETE GUARANTEE MUST SAY SO. CLAUDE.md, on
            // grainBushelsFromCoverage: "a dropped line makes the guarantee read
            // short, which makes % sold read high, so it can never be silent."
            // Meisenzahl CY2026 soybeans is exactly that case and is how Kevin
            // caught this: his LIVINGSTON line carries an APH of 53 and NO acres
            // and NO liability, so it contributes nothing to the denominator —
            // 27,000 bu committed renders as 146% of an 18,492 bu guarantee
            // built from two counties instead of three. The farm card prints the
            // warning; the bar was showing the bare number.
            //
            // THE MARKER IS NEUTRAL ('*'), NOT A DIRECTION, and that is
            // deliberate — grainRules.js raises three different reasons and they
            // do NOT all push the same way:
            //   'no liability and no APH/acres'  -> the row is DROPPED, so the
            //       guarantee is short and the % reads HIGH (Meisenzahl);
            //   'liability keyed but acres not reported' -> the row still counts
            //       toward bushels, only acres/APH weighting is skipped;
            //   'prevent-plant acres not loaded' -> PP counted as planted, so
            //       the guarantee is BIG and the % reads LOW.
            // Stamping '≥' on all three would assert a direction two of them do
            // not have. The tooltip names the reason verbatim instead.
            const incomplete = (pos?.exposure?.incomplete || []);
            crops.push({
              crop, pct: Math.round(pct), committed, expected,
              incomplete: incomplete.length,
              why: incomplete.map((i) => `${i.county || '—'}: ${i.why}`).join(' · '),
            });
          }
          if (crops.length) out[g.key] = crops;
        }
        if (!cancelled) setSoldByFarmer(out);
      } catch { /* leave the bars off rather than block the picker */ }
    }
    load();
    return () => { cancelled = true; };
  }, [uniqueFarmers, contractCounts, cropYear]);

  // The bars for one farm — one per crop it actually has contracts on, because
  // corn bushels and soybean bushels are different products and a single
  // blended "% sold" would be a number that is neither.
  const soldBars = (f) => {
    const crops = soldByFarmer[farmKey(f)];
    if (!crops || !crops.length) return null;
    return (
      <div style={{ marginTop: 6 }}>
        {crops.map((c) => (
          <div key={c.crop} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}
            title={`${c.crop} ${cropYear}: ${fmt(c.committed, 0)} of ${fmt(c.expected, 0)} bu committed`
              + (c.incomplete ? `\n⚠ the guarantee behind this % is not final — ${c.why}` : '')}>
            <span style={{ width: 58, flexShrink: 0, fontSize: 11, color: '#60735f' }}>{c.crop}</span>
            <div style={{ flex: 1, height: 5, borderRadius: 3, background: '#eef3ec', overflow: 'hidden', minWidth: 40 }}>
              {/* An incomplete guarantee is drawn STRIPED, never as a flat
                  confident fill — the denominator is missing a coverage line
                  and the number reads high. The stripe is the signal; the
                  tooltip names the county. */}
              <div style={{
                width: `${Math.min(100, c.pct)}%`, height: '100%',
                background: c.incomplete
                  ? `repeating-linear-gradient(45deg, ${c.pct > 100 ? '#a13a2f' : '#2d6a1a'} 0 3px, #cbd8c6 3px 6px)`
                  : (c.pct > 100 ? '#a13a2f' : '#2d6a1a'),
              }} />
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: c.pct > 100 ? '#a13a2f' : '#1a2e1a', width: 30, textAlign: 'right' }}>
              {c.incomplete ? '*' : ''}{c.pct}%
            </span>
          </div>
        ))}
      </div>
    );
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? uniqueFarmers.filter(f => (f.name || '').toLowerCase().includes(q))
    : uniqueFarmers;

  const farmerContractCount = (f) => {
    const ids = f.allFarmerDbIds || [f.id].filter(Boolean);
    return ids.reduce((s, id) => s + (contractCounts[id] || 0), 0);
  };

  // Surface farms with active contracts in their own section at the top, ordered
  // by contract count (most active first); everything else falls below. Both
  // sections respect the search filter.
  const withContracts = filtered
    .filter(f => farmerContractCount(f) > 0)
    .sort((a, b) => farmerContractCount(b) - farmerContractCount(a) || (a.name || '').localeCompare(b.name || ''));
  const withoutContracts = filtered.filter(f => farmerContractCount(f) === 0);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* A light heading, not a dark hero block: the farm list below is the
          content, and the banner pushed it a third of a screen down. */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>Select a farm</div>
        <div style={{ fontSize: 12.5, color: T.muted, marginTop: 2 }}>Track contracts, forward pricing, and profitability per farm.</div>
      </div>

      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 280px', position: 'relative' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search farmers…"
            style={{
              // border-box: width:100% plus 52px of padding ran off the right
              // edge of a phone and over the farm count beside it on desktop.
              width: '100%', boxSizing: 'border-box', padding: '10px 14px 10px 38px',
              border: '1px solid #c8d8c0', borderRadius: 9, fontSize: 14,
              background: '#fff', color: '#1a2e1a', fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#60735f', fontSize: 14 }}>🔎</span>
        </div>
        <div style={{ fontSize: 11, color: '#60735f' }}>
          {filtered.length} of {uniqueFarmers.length} {uniqueFarmers.length === 1 ? 'farm' : 'farms'}
        </div>
      </div>

      {(() => {
        const gridStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 };
        const sectionLbl = { fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2d4a2d', margin: '4px 2px 9px' };
        const renderCard = (f) => {
          const count = farmerContractCount(f);
          const counties = f._allCounties || (f.county ? [f.county] : []);
          const policyCount = f._allPolicyNums?.length || 0;
          return (
            <button
              key={f.id}
              onClick={() => onSelect(f)}
              style={{
                ...cardStyle,
                cursor: 'pointer',
                textAlign: 'left',
                display: 'block',
                width: '100%',
                marginBottom: 0,
                position: 'relative',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#2d4a2d'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(45,74,45,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#dde8d8'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1a2e1a', fontFamily: "'Lora',serif", flex: 1 }}>{f.name}</div>
                {count > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 12, background: '#eaf4e4', color: '#2d5a10', whiteSpace: 'nowrap' }}>
                    {count} contract{count === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#60735f', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {counties.length > 0 && (
                  <div>{counties.slice(0, 3).join(' · ')}{counties.length > 3 ? ` +${counties.length - 3}` : ''}</div>
                )}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {policyCount > 0 && <span>{policyCount} {policyCount === 1 ? 'policy' : 'policies'}</span>}
                  {f.customer_type === 'grain_only' && <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: '#fef9ec', color: '#c8860a' }}>GRAIN ONLY</span>}
                  {count === 0 && <span style={{ fontSize: 10, color: '#a8b8a8', fontStyle: 'italic' }}>no contracts yet</span>}
                </div>
                {soldBars(f)}
              </div>
            </button>
          );
        };

        if (filtered.length === 0) {
          return (
            <div style={{ ...cardStyle, textAlign: 'center', color: '#60735f' }}>
              {q ? `No farmers match "${q}"` : 'No farmers on file.'}
            </div>
          );
        }
        return (
          <>
            {withContracts.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={sectionLbl}>📑 Active Contracts · {withContracts.length}</div>
                <div style={gridStyle}>{withContracts.map(renderCard)}</div>
              </div>
            )}
            {/* The full book only appears when searching — keeps this page to the
                handful of farms you're actively marketing. */}
            {q ? (
              <div>
                {withContracts.length > 0 && <div style={sectionLbl}>Other Matches · {withoutContracts.length}</div>}
                <div style={gridStyle}>{withoutContracts.map(renderCard)}</div>
              </div>
            ) : (
              withoutContracts.length > 0 && (
                <div style={{ ...cardStyle, textAlign: 'center', color: '#60735f', fontSize: 13 }}>
                  🔎 {withoutContracts.length} more {withoutContracts.length === 1 ? 'farm' : 'farms'} without contracts — type a name above to pull one up.
                </div>
              )
            )}
          </>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// useGrainActionResult / GrainActionSummaryPanel — Silas Grain Action, the
// deterministic exposure/economics/market/action pipeline (ROADMAP.md §1.2b,
// Phase IV: "Shadow/admin only until validated. No farmer-facing
// recommendations."). Originally a standalone `GrainActionScreen` nav item
// (§1.2c); folded into GrainMarketingView's own "Summary" tab per Kevin's
// SILAS_ACTION_INTELLIGENCE.md Milestone 2 ("integrate it as the Summary of
// the existing Grain Marketing product... do not create two competing Grain
// tools" — see ROADMAP.md §1.2f). Split into a data hook and a presentational
// panel so there is exactly ONE implementation of this rendering, not a
// hand-copy sitting beside GrainMarketingView's own dashboard.
//
// useGrainActionResult fetches only what a caller doesn't already have —
// crop_coverage (for exposure) and futures bar history (for market context).
// contracts/copRows are passed in: GrainMarketingView already loads both via
// grainLoadAll() for its own tabs, so a second independent fetch here would
// be exactly the duplicated-implementation trap this refactor exists to
// remove. Pass farmer=null to disable outright — no fetch, no compute, no
// result — which is how the caller keeps this off a portal/non-admin session
// (CLAUDE.md "hide the surface, then guard the fetch": the FETCH is gated,
// not only the render).
function useGrainActionResult({ farmer, cropYear, crop, contracts, copRows }) {
  const [coverageRows, setCoverageRows] = useState([]);
  // grain_crop_plan, read here for ONE field: chopped_acres. The Marketing Tool
  // owns the writing of it; this surface only has to agree with what it says.
  const [planRows, setPlanRows] = useState([]);
  const [marketBars, setMarketBars] = useState({ contBars: [], contHist: [], sym: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Storage-save optimistic override (Kevin, 2026-08-19 mobile review: "If
  // storage capacity is entered/saved, the screen must reflect and use it
  // consistently; no stale 'capacity not stated' state"). `result` below is
  // a useMemo keyed on `farmer.metadata` — GrainStorageEditor's own PATCH
  // writes straight to the farmers table and never updates the `farmer`
  // OBJECT this hook was called with, so without this override the
  // storage-exposure split stayed stale (showing the pre-save state) until
  // an unrelated farmer refetch happened to land. onStorageSaved lets the
  // editor hand back exactly what it just persisted so the SAME render
  // picks it up — never a second network read, never a fabricated value.
  const [storageOverride, setStorageOverride] = useState(null);
  useEffect(() => { setStorageOverride(null); }, [farmer?.id]);

  useEffect(() => {
    if (!farmer) { setCoverageRows([]); setPlanRows([]); return; }
    let alive = true;
    setLoading(true); setError(null);
    (async () => {
      try {
        const ownerIds = farmer.allFarmerDbIds?.length ? farmer.allFarmerDbIds : [farmer.id];
        const pols = await sbFetch('policies', { select: 'id', filters: [`farmer_id=in.(${ownerIds.join(',')})`, `crop_year=eq.${cropYear}`] }).catch(() => []);
        const pids = pols.map(p => p.id);
        const [rows, plans] = await Promise.all([
          pids.length
            ? sbFetch('crop_coverage', { select: 'county,crop,type,type_code,practice_code,plan,aph_yield,coverage_level,acres,pp_acres,liability,price_election,price_election_pct', filters: [`policy_id=in.(${pids.join(',')})`] }).catch(() => [])
            : Promise.resolve([]),
          // acres / yield_per_acre / aph_yield / actual_production are read by
          // grainSummary.js's production-source ladder — the SAME precedence
          // (actual > keyed > APH) GrainMarketingView's own yieldInfo() already
          // applies on the Dashboard tab. Selecting only chopped_acres is what
          // left the Summary quoting RMA's 145,502 bu while the Dashboard beside
          // it quoted the Crop Plan's 146,890 for the same farm and crop.
          sbFetch('grain_crop_plan', { select: 'crop,chopped_acres,acres,yield_per_acre,aph_yield,actual_production', filters: [`farmer_id=in.(${ownerIds.join(',')})`, `crop_year=eq.${cropYear}`] }).catch(() => []),
        ]);
        if (alive) { setCoverageRows(rows); setPlanRows(plans); }
      } catch (e) {
        if (alive) setError(String((e && e.message) || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [farmer, cropYear]);

  // Market: this crop year's new-crop board (grainBoardSymForMonth — the same
  // board GRAIN_NEWCROP_MO already names for this exact crop/year relationship
  // elsewhere in this file).
  useEffect(() => {
    if (!farmer || !crop || !cropYear) { setMarketBars({ contBars: [], contHist: [], sym: null }); return; }
    let alive = true;
    (async () => {
      const commodityLower = crop.toLowerCase();
      const newCropMo = GRAIN_NEWCROP_MO[crop] || 12;
      const sym = grainBoardSymForMonth(commodityLower, cropYear, newCropMo);
      const [contBars, contHist] = await Promise.all([
        sym ? sbFetch('futures_contract_history', { select: 'bar_date,close,high', filters: [`commodity=eq.${commodityLower}`, `contract_sym=eq.${sym}`], order: 'bar_date.asc' }).catch(() => []) : [],
        sbFetch('futures_history_daily', { select: 'bar_date,close', filters: [`commodity=eq.${commodityLower}`], order: 'bar_date.asc' }).catch(() => []),
      ]);
      if (alive) setMarketBars({ contBars, contHist, sym });
    })();
    return () => { alive = false; };
  }, [farmer, crop, cropYear]);

  // Cash pricing (Kevin, 2026-08-17 spec §2/§3/§4): "the farm-level sale
  // recommendation and economic comparison must use a realizable CASH price
  // whenever basis is available." Reuses the SAME two tables the Market
  // Intelligence Basis tab / GrainTargetPlanner already read — no parallel
  // fetch, no parallel basis engine (src/positions/grainCash.js). Note the
  // same commodity-key mismatch GrainTargetPlanner already documents:
  // basis_calculated_daily keys soybeans 'soy'; grain_basis_board keys them
  // 'soybeans' — getting this wrong silently empties the cash column.
  const [cashRows, setCashRows] = useState({ basisRows: [], boardRows: [] });
  useEffect(() => {
    if (!farmer || !crop) { setCashRows({ basisRows: [], boardRows: [] }); return; }
    let alive = true;
    const commodity = GRAIN_CROP_TO_COMMODITY[crop];
    const basisCommodity = crop === 'Soybeans' ? 'soy' : commodity;
    const since = new Date(Date.now() - 10 * 86400000).toISOString().slice(0, 10);
    // basis_sheet_observations is the DEPTH here, and without it this panel
    // could never resolve a basis percentile at all. basis_calculated_daily has
    // NO bid-structure column, so every one of its rows resolves to
    // bidStructure "unknown" and filterComparableBasisHistory excludes all of
    // them — which is exactly what the panel was reporting: "1246 historical
    // row(s) on file for howlett_leroy_tdag, but none share a confirmed
    // comparable bid structure (unknown)". The rows were there; not one could
    // be compared. It is also only a 10-day window, so even structured it would
    // be a fortnight of history, not a history.
    //
    // The transcribed archive carries the structure explicitly and spans five
    // crop years, so it is mapped into the same row shape and concatenated. The
    // live 10-day rows stay: they are the freshest read of the same boards.
    const archiveCommodity = crop === 'Soybeans' ? 'soybeans' : commodity;
    Promise.all([
      sbFetch('basis_calculated_daily', {
        filters: [`commodity=eq.${basisCommodity}`, `bid_date=gte.${since}`],
        select: 'source,delivery_contract,delivery_basis,delivery_futures_price,cash_price,bid_date', order: 'bid_date.desc',
      }).catch(() => []),
      // Gated on the Cash Board switch, like every reader of Howlett's posted bids.
      howlettBidsVisibleP().then(ok => !ok ? [] : sbFetch('grain_basis_board', {
        filters: [`commodity=eq.${commodity}`],
        // `term` is REQUIRED here, not decorative: buildBidStructureIndex()
        // skips any row without it, so omitting it produced an empty index,
        // every candidate resolved bid structure UNKNOWN, and the basis
        // gauge could never compare anything to anything.
        select: 'location,term,cme_contract,ship_month,basis,updated_at',
      })).catch(() => []),
      sbFetch('basis_sheet_observations', {
        filters: [`commodity=eq.${archiveCommodity}`, 'no_bid=is.false'],
        select: 'source,bid_structure,futures_contract,sheet_date,basis,commodity',
      }).catch(() => []),
    ]).then(([basisRows, boardRows, archiveRows]) => {
      if (!alive) return;
      // A NO_BID is already excluded by the filter above — it is a market state,
      // never a low number, and must not enter a percentile.
      const mapped = (archiveRows || []).map(r => ({
        // HISTORY ONLY — never a candidate for today's cash bid. These rows
        // are transcribed sheets going back to 2021 with no cash price; one
        // surfacing as a current example bid would quote a years-old basis.
        historyOnly: true,
        crop: r.commodity,
        source: r.source,
        term: r.bid_structure,            // the field the structure gate reads
        delivery_basis: r.basis,
        bid_date: r.sheet_date,
        delivery_contract: r.futures_contract || null,
      }));
      setCashRows({ basisRows: [...(basisRows || []), ...mapped], boardRows: boardRows || [] });
    });
    return () => { alive = false; };
  }, [farmer, crop]);

  // farmGeo (2026-08-18 §A): farmer.lat/lon are already on this object — the
  // main farmers fetch selects every column, no second query needed. Absent
  // on a farm with no address on file; resolveGrainCash degrades to
  // geographyConfidence 'unknown' rather than guessing a location.
  const farmGeo = (farmer && farmer.lat != null && farmer.lon != null) ? { lat: Number(farmer.lat), lon: Number(farmer.lon) } : null;

  // Forward curve (Kevin, 2026-08-18: "stop anchoring the advisor to a single
  // nearby board month... expose all relevant forward futures contracts for
  // the selected crop year"). Reuses deliveryMonthsForCropYear() — the SAME
  // month list the Marketing Plan tool already builds its schedule from, not
  // a second enumeration — mapped through grainBoardSymForMonth() into the
  // exact contract_sym format futures_contract_history stores (that mapping
  // is the identity map here: FUTURES_MONTHS_BY_CROP and grainTargets.js's
  // GRAIN_LISTED_MONTHS name the same listed months, so no roll ever fires).
  // One extra sbFetch per contract in the crop year (~5-7), all in parallel,
  // for the one farm/crop currently open — not run per Book View row.
  const [curveBars, setCurveBars] = useState([]);
  useEffect(() => {
    if (!farmer || !crop || !cropYear) { setCurveBars([]); return; }
    let alive = true;
    (async () => {
      const commodityLower = crop.toLowerCase();
      const newCropMo = GRAIN_NEWCROP_MO[crop] || 12;
      const months = deliveryMonthsForCropYear(crop, cropYear) || [];
      const contractDefs = months.map(label => {
        const [mn, yr2] = label.split(' ');
        const year = 2000 + parseInt(yr2, 10);
        const monthNum = _MONTH_NUM[mn];
        const sym = monthNum ? grainBoardSymForMonth(commodityLower, year, monthNum) : null;
        return { sym, label, isNewCrop: monthNum === newCropMo && year === Number(cropYear) };
      }).filter(c => c.sym);
      const barsPerContract = await Promise.all(contractDefs.map(c =>
        sbFetch('futures_contract_history', { select: 'bar_date,close,high', filters: [`commodity=eq.${commodityLower}`, `contract_sym=eq.${c.sym}`], order: 'bar_date.asc' }).catch(() => [])
      ));
      const next = contractDefs.map((c, i) => {
        const bars = barsPerContract[i] || [];
        return {
          sym: c.sym, label: c.label, isNewCrop: c.isNewCrop,
          // ⚠ NO LOCAL BAR FLOOR. grainContractTech already refuses below
          // GRAIN_MIN_BARS and returns null, so a length guard here was a SECOND COPY
          // of that constant — harmless while the two agreed, and a silent divergence
          // the moment the engine's floor moved. Lowering it there would leave these
          // call sites still refusing contracts the engine had just learned to read.
          tc: grainContractTech(bars),
          // Trend is measured on this contract's own closes over a STATED
          // number of sessions and stays a SEPARATE reading from level — a
          // historically elevated price that has begun falling and one still
          // rising are different reads, and one badge cannot carry both.
          trend: sessionTrend(bars.map((b) => b.close), 20),
        };
      });
      if (alive) setCurveBars(next);
    })();
    return () => { alive = false; };
  }, [farmer, crop, cropYear]);

  // ── The Summary's ONE futures comparison, for the crop-year's ANCHOR board.
  // Same population as the gauge's cross-year score (identical
  // filterComparableFuturesHistory rule inside rankContractAmongYears), read
  // one observation per YEAR instead of one per bar — see grainSummary.js's
  // §5 for the two measured reasons a bar-count percentile overstates what
  // six comparable years can support. Fetched for the anchor rather than the
  // clicked tile so the Summary's primary read never changes because someone
  // browsed the curve.
  // One entry per curve contract, keyed by sym, so EVERY row of the Summary's
  // market table is compared on the same population as every other row. Six
  // prior contract years per delivery month is a fetch-volume choice, not a
  // scoring rule — rankContractAmongYears' own minContractYears decides whether
  // that is enough to say anything.
  const [curveYearRanks, setCurveYearRanks] = useState({});
  useEffect(() => {
    if (!farmer || !crop || !cropYear) { setCurveYearRanks({}); return; }
    const commodityLower = crop.toLowerCase();
    const points = (curveBars || []).filter((c) => c && c.tc && c.label);
    if (!points.length) { setCurveYearRanks({}); return; }
    let alive = true;
    (async () => {
      const out = {};
      await Promise.all(points.map(async (pt) => {
        const [mn, yr2] = String(pt.label).split(' ');
        const monthNum = _MONTH_NUM[mn];
        const targetYear = (mn && yr2) ? 2000 + parseInt(yr2, 10) : null;
        if (!monthNum || !targetYear) return;
        const years = [1, 2, 3, 4, 5, 6].map((n) => targetYear - n);
        const barsPerYear = await Promise.all(years.map((y) => {
          const sym = grainBoardSymForMonth(commodityLower, y, monthNum);
          if (!sym) return Promise.resolve([]);
          return sbFetch('futures_contract_history', { select: 'bar_date,close', filters: [`commodity=eq.${commodityLower}`, `contract_sym=eq.${sym}`], order: 'bar_date.asc' }).catch(() => []);
        }));
        const history = [];
        years.forEach((y, i) => {
          (barsPerYear[i] || []).forEach((b) => {
            if (b && b.bar_date && b.close != null) history.push({ crop: commodityLower, contractMonth: mn.toUpperCase(), contractYear: y, obsDate: b.bar_date, settle: Number(b.close) });
          });
        });
        const targetOffsetDays = contractLifeOffsetDays({ contractMonth: mn, contractYear: targetYear, obsDate: pt.tc.asOf });
        out[pt.sym] = rankContractAmongYears({ crop: commodityLower, contractMonth: mn, targetContractYear: targetYear, targetOffsetDays, targetSettle: pt.tc.last, history });
      }));
      if (alive) setCurveYearRanks(out);
    })();
    return () => { alive = false; };
  }, [farmer, crop, cropYear, curveBars]);

  const result = useMemo(() => {
    if (!farmer) return null;
    // storageOverride, when set, wins over farmer.metadata for the
    // grainStorage key ONLY — everything else about the farmer's metadata
    // is untouched. See the override's own comment above for why this
    // exists instead of relying on a farmer refetch.
    const effectiveMetadata = storageOverride ? { ...(farmer.metadata || {}), grainStorage: storageOverride } : farmer.metadata;
    return computeGrainActionResult({ farmerName: farmer.name, crop, cropYear, coverageRows, contractRows: contracts, copRows, marketBars, basisRows: cashRows.basisRows, boardRows: cashRows.boardRows, farmGeo, curveBars, farmerMetadata: effectiveMetadata, planRows, curveYearRanks, asOf: new Date().toISOString().slice(0, 10) });
  }, [farmer, crop, cropYear, coverageRows, planRows, contracts, copRows, marketBars, cashRows, farmGeo?.lat, farmGeo?.lon, curveBars, storageOverride, curveYearRanks]);

  return { result, loading, error, onStorageSaved: setStorageOverride };
}

// computeGrainActionResult — the pure compute step useGrainActionResult()
// wraps in a hook, pulled out so GrainBookView (ROADMAP.md §1.2f Milestone 4)
// can run the SAME position/economics/market/action pipeline across the
// whole book without one React hook per farmer (hooks cannot run inside a
// loop). marketBars is the crop's board data — the caller fetches it ONCE
// per crop and reuses it for every farmer growing that crop, not once per
// farmer; it is the same market for all of them.
// planRows: this farm's grain_crop_plan rows for cropYear. The only thing read
// off them here is `chopped_acres` — acres insured as GRAIN the grower will chop,
// which cannot back a grain sale. Omit them and every farm reads as chopping
// nothing, which is correct for all but the handful who do; pass them and the
// book view agrees with the farm's own Crop Plan instead of quietly reading fuller.
function computeGrainActionResult({ farmerName, crop, cropYear, coverageRows, contractRows, copRows, marketBars, basisRows = [], boardRows = [], farmGeo = null, curveBars = [], farmerMetadata = null, planRows = [], curveYearRanks = {}, asOf = null }) {
  const position = buildGrainPosition({ farm: farmerName, crop: crop.toUpperCase(), cropYear, coverageRows, contractRows, choppedAcres: choppedAcresFor(planRows, crop) });
  // Conditional position banner (Kevin, 2026-08-18): "do not ask Kevin or the
  // farmer to manually provide a grain position." Summarizes whatever
  // GrainPosition already resolved from verified data into known/partial/
  // unknown — never a fabricated 0% when contract completeness is unknown.
  const positionBanner = positionBannerState(position);

  // Economics: only this farm's own hand-entered grain_cop row — never a
  // benchmark (Phase I-A's benchmark baseline doesn't exist yet; see
  // ROADMAP.md §1.2b/§1.2k). Absent → cop stays null → buildGrainAction
  // withholds breakeven/required-price rather than inventing them.
  const copRow = (copRows || []).find(r => String(r.crop).toLowerCase() === crop.toLowerCase() && Number(r.crop_year) === Number(cropYear));
  let cop = null;
  if (copRow && copRow.line_items && position?.exposure?.aph) {
    const items = copRow.line_items;
    const mode = Object.keys(items).some(k => k.startsWith('s_')) ? 'streamlined' : 'detailed';
    cop = resolveGrainCop({ mode, crop, lineItems: items, yieldPerAcre: position.exposure.aph });
  }

  const { contBars = [], contHist = [], sym = null } = marketBars || {};
  const tc = grainContractTech(contBars);   // the engine owns the minimum — see above
  const curve = contHist.length ? grainFillCurve(contHist) : null;
  // GRAIN_NEWCROP_MO's months are all listed CBOT months for their crop
  // (Dec corn, Jul wheat, Nov soy), so grainBoardSymForMonth never rolls to
  // the next year here — the delivery year IS cropYear.
  const newCropMo = GRAIN_NEWCROP_MO[crop] || 12;
  const contractDate = `${cropYear}-${String(newCropMo).padStart(2, '0')}-01`;

  // Cash pricing (Kevin, 2026-08-17 spec §2): resolved from whatever basis/
  // board rows the caller already fetched for this crop — never fetched here
  // (this function stays a pure compute step, matching cop's own pattern
  // just above). basisRows/boardRows default to [] so a caller that hasn't
  // wired cash yet (none exist today) degrades to "cash unavailable" rather
  // than a crash. farmGeo (2026-08-18 §A) is the farmers.lat/lon this crop's
  // farmer already carries — optional, so a farm with no address on file
  // still resolves cash, just with geographyConfidence 'unknown'.
  const cash = resolveGrainCash({ crop, contractDate, basisRows, boardRows, farmGeo });

  // Storage strategy (Kevin, 2026-08-18 20:40: "a pure data/model seam — no
  // calendar deadlines, forced-sale %, or carrying-cost economics yet").
  // Reads the SAME farmers.metadata jsonb column drpEstProd already lives in
  // — no new table, no migration. buildGrainAction() does not read this for
  // any calculation; it is a passthrough so the UI can show/collect it ahead
  // of the future timing-model layer that will actually consume it.
  const storage = resolveGrainStorage(farmerMetadata);

  // Regime lens (2026-08-18 "GRAIN MARKET INTELLIGENCE UI" item 1) — a
  // SEPARATE current-regime classifier from tc.pctile above, never collapsed
  // into it. rows: the SAME contBars already fetched for tc (no new fetch).
  // Curve structure: the new-crop board (`sym`, nearby) vs. the next LATER
  // delivery contract in curveBars that has resolved data (deferred) — both
  // real, already-fetched settles from the SAME curveBars effect
  // buildForwardCurve() below also reads, not a second fetch.
  const nearbyIdx = (curveBars || []).findIndex(c => c && c.sym === sym);
  const deferredPoint = nearbyIdx >= 0
    ? (curveBars || []).slice(nearbyIdx + 1).find(c => c && c.tc && c.tc.last != null)
    : null;
  const regime = classifyMarketRegime({
    rows: contBars, tc,
    curveNearbySettle: tc ? tc.last : null,
    curveDeferredSettle: deferredPoint ? deferredPoint.tc.last : null,
  });

  // asOf (2026-08-19 correction — sourced CFFM/Usset marketing-stage policy,
  // src/positions/grainMarketingStagePolicy.js): the one real "today" this
  // PURE compute step needs to place the farm's crop-year within CFFM's own
  // discipline calendar. grainTargetPosition.js/grainMarketingStagePolicy.js
  // themselves take no clock of their own (never Date.now()/new Date()) —
  // App.jsx is the one layer with a real clock, so it supplies it here, the
  // same way every other `new Date()` call in this file already does.
  // A caller may supply it (the Summary hook does, so one render's stage,
  // objective-age and bid-age all read the SAME day); otherwise this reads the
  // clock itself, exactly as before.
  const asOfDay = asOf || new Date().toISOString().slice(0, 10);
  // BUG-011. The status ladder ranks the board ACROSS YEARS, never against its
  // own life-to-date closes: futures_contract_history stores an expired contract
  // as a ~250-bar one-year segment and a live one as its whole trading life, so
  // all twenty live corn/soybean/wheat contracts read 98.6-100.0 on 2026-09-03
  // the ladder's own "80th percentile or better" rung returned STRONG for
  // every farm, every crop, every day. The same
  // twenty ranked among years spread 50.0-100.0.
  //
  // This is the SAME rank the Summary already renders (curveYearRanks, keyed by
  // sym) — read here rather than recomputed, so the decision and the table
  // beside it can never disagree about where the board sits. Absent, the action
  // engine withholds the market leg instead of falling back to the biased one.
  const action = buildGrainAction({ position, cop, tc, curve, contractDate, cash, storage, regime, asOf: asOfDay,
    futuresYearRank: (curveYearRanks && sym) ? (curveYearRanks[sym] || null) : null });

  // Full forward curve (Kevin, 2026-08-18) — every listed delivery contract
  // for this crop year, never just the nearby board. Additive: `action`
  // above still evaluates ONE board (the new-crop contract) for economics,
  // exactly as before — the curve is a separate view, not a second action
  // engine.
  const forwardCurveBase = buildForwardCurve({ crop, cropYear, contractBars: curveBars });

  // The transcribed archive, normalized once for the market-level basis
  // aggregate below. These are the SAME rows already fetched for the history
  // sample (flagged historyOnly so they can never be quoted as a live bid).
  const basisCrop = String(crop || '').toLowerCase() === 'soybeans' ? 'soybeans' : String(crop || '').toLowerCase();
  const archiveHistory = (basisRows || []).filter((r) => r && r.historyOnly)
    .map(normalizeObservation).filter(Boolean);

  // Per-tile implied cash (2026-08-18 "COMPLETE THE VISIBLE GRAIN GAUGE
  // MILESTONE" item 2: "for each relevant delivery period tile/point
  // preserve separately... implied cash where defensible"). Pure reuse of
  // the SAME resolveGrainCash() the economics anchor calls above, against
  // the SAME basisRows/boardRows already fetched for this crop — no new
  // fetch, no second cash/basis engine. Each point resolves against ITS OWN
  // delivery month/year, so a deferred tile is priced off its own basis
  // rows, never silently inherits the anchor contract's cash. A tile with no
  // resolvable price (no data) or an unparseable label gets cash:null —
  // never a fabricated price.
  const forwardCurve = {
    ...forwardCurveBase,
    points: forwardCurveBase.points.map((p) => {
      const [pmn, pyr2] = String(p.label || '').split(' ');
      const pMonthNum = _MONTH_NUM[pmn];
      const pYear = (pmn && pyr2) ? 2000 + parseInt(pyr2, 10) : null;
      if (!pMonthNum || !pYear) return { ...p, cash: null };
      const pContractDate = `${pYear}-${String(pMonthNum).padStart(2, '0')}-01`;
      const pCash = resolveGrainCash({ crop, contractDate: pContractDate, basisRows, boardRows, farmGeo });
      // MARKET-LEVEL basis strength for THIS tile — every posted location and
      // both bid structures, each measured against its own normal. Computed
      // per point so selecting a tile moves it; the old gauge read the
      // anchor's cash and could never respond to a click.
      const pSym = pCash?.deliveryContract || null;
      const pSeen = new Set();
      const pCurrent = [];
      for (const br of boardRows || []) {
        if (!br || br.basis == null || br.cme_contract !== pSym) continue;
        const src = slugForMarket(br.location) || unmappedSlug(br.location);
        const k = src + '|' + br.term;
        if (pSeen.has(k)) continue;
        pSeen.add(k);
        pCurrent.push({ source: src, sourceLabel: br.location, bidStructure: br.term, basis: Number(br.basis) });
      }
      const pAgg = aggregateBasisStrength({
        commodity: basisCrop, contract: pSym, obsDate: asOfDay,
        current: pCurrent, history: archiveHistory,
      });
      return { ...p, cash: pCash, basisAggregate: pAgg };
    }),
  };

  // This crop-year's contracts, for the cash-flow chart AND for the harvest-
  // outlet arithmetic. buildGrainPosition collapses them into totals; both of
  // those need each contract's own delivery window. Filtered through
  // grainPosition.js's OWN cropMatches + crop_year rule — handing over the raw
  // book drew every crop and every year on one crop's card.
  const cropContracts = (contractRows || []).filter((c) =>
    Number(c.crop_year) === Number(cropYear) && cropMatches(c.crop, crop.toUpperCase()));


  return { position, cop, action, sym, barsFound: contBars.length, histFound: contHist.length, positionBanner, forwardCurve,
    contracts: cropContracts };
}





// PlanNumberInput — AN UNCONTROLLED BOX THAT RE-SEEDS WITHOUT REMOUNTING.
//
// Kevin, 2026-09-04: "as soon as you type 1 digit it locks up and you cant keep
// typing... you have to click in the box to type another digit". Every Crop Plan
// number box carried its OWN VALUE INSIDE ITS `key`:
//
//   <input key={`ac-${crop}-${cropYear}-${plan.acres ?? ''}`}
//          defaultValue={...} onChange={e => handlePlanChange(...)} />
//
// `handlePlanChange` calls `setPlanRows` synchronously, so the first keystroke
// changed `plan.acres`, which changed the key, which made React UNMOUNT AND
// REMOUNT the input. A remounted element is a different DOM node: the caret and
// the focus go with the old one. Typing 546 took three clicks.
//
// The value was in the key for a real reason and it must not simply be deleted:
// these boxes are UNCONTROLLED (`defaultValue`), so a remount was the only thing
// re-seeding them when the number arrived from somewhere ELSE — planRows landing
// from the DB, or `planBasis` falling back to the acres/APH the policy reports
// once crop_coverage loads. Drop the key and a box that was blank at first paint
// stays blank over real data.
//
// So the key becomes STABLE and the re-seed becomes explicit:
//   • the box mounts once per (crop, crop year) and is never remounted by typing;
//   • an OUTSIDE change writes through to the DOM node — but only when the box is
//     not focused, so an async load can never overwrite what is being typed;
//   • on blur it normalises to the stored figure, which is what the remount used
//     to do (a cleared box falls back to the policy's own number rather than
//     sitting empty against a card that shows one).
//
// `seed` is the ALREADY-FORMATTED display value the old `defaultValue` produced.
// Guarded by tools/check-input-remount.js, which fails the build on any
// uncontrolled onChange input that puts its own value back in its key.
function PlanNumberInput({ seed, onChange, onBlur, ...rest }) {
  const ref = useRef(null);
  const text = seed == null || seed === '' ? '' : String(seed);
  // The latest rendered seed, readable from an event handler. onChange has
  // already committed by the time blur fires, so this is the stored figure.
  const seedRef = useRef(text);
  seedRef.current = text;
  const lastSeed = useRef(text);
  useEffect(() => {
    if (text === lastSeed.current) return;
    lastSeed.current = text;
    const el = ref.current;
    if (!el) return;
    if (typeof document !== 'undefined' && document.activeElement === el) return;
    el.value = text;
  }, [text]);
  return (
    <input
      ref={ref}
      defaultValue={text}
      onChange={onChange}
      onBlur={ev => {
        if (onBlur) onBlur(ev);
        const el = ref.current;
        if (el && el.value !== seedRef.current) { el.value = seedRef.current; lastSeed.current = seedRef.current; }
      }}
      {...rest}
    />
  );
}


// GrainStorageEditor — the storage-strategy SEAM's UI half (Kevin, 2026-08-18
// 20:40: "add a two-choice storage-strategy seam — Deliver at Harvest / Has
// On-Farm Storage, optional capacity bushels — as a pure data/model seam. No
// calendar deadlines, forced-sale %, or carrying-cost economics yet"). Reads
// and writes farmers.metadata.grainStorage via the SAME PATCH-merge pattern
// DRPProtectionPlanner's onPersist already uses for drpEstProd — no new
// table, no migration, no second persistence mechanism. Renders nothing if
// there's no real farmer to attach the setting to (Book View / a farmer-less
// caller never sees this control). Deliberately does NOT feed the chosen
// mode/capacity into any status, sizing, or economics — buildGrainAction()
// only ever passes `storage` straight through unread (see grainStorage.js).
function GrainStorageEditor({ farmer, storage, onSaved = null }) {
  const initial = storage || { known: false, mode: null, capacityBu: null };
  const [mode, setMode] = useState(initial.mode);
  const [capacityInput, setCapacityInput] = useState(initial.capacityBu != null ? String(initial.capacityBu) : '');
  const [saving, setSaving] = useState(false);
  // Re-seed local state when a DIFFERENT farm/crop's storage object arrives
  // (switching farms) — never overwrite mid-edit on the SAME farm's own
  // re-render (e.g. after a save round-trips a fresh `storage` prop with an
  // identical value, this would otherwise just be a no-op re-seed anyway).
  useEffect(() => { setMode(initial.mode); setCapacityInput(initial.capacityBu != null ? String(initial.capacityBu) : ''); }, [farmer?.id]); // eslint-disable-line

  if (!farmer?.id) return null;

  // Kevin, 2026-08-19 mobile review: "If storage capacity is entered/saved,
  // the screen must reflect and use it consistently; no stale 'capacity not
  // stated' state." The PATCH below writes to the DB but never updated the
  // `farmer` object this screen's own action/exposure split was computed
  // from, so the harvest-pressure line kept showing the PRE-save state.
  // onSaved (useGrainActionResult's setStorageOverride) hands the exact
  // just-persisted value back so the very next render recomputes
  // deriveStorageExposure() off it — no second fetch, no stale text.
  function persist(nextMode, nextCapacityBu) {
    const next = { mode: nextMode, capacityBu: nextCapacityBu, updatedAt: new Date().toISOString().slice(0, 10) };
    if (onSaved) onSaved(next);
    fetch(`${SUPABASE_URL}/rest/v1/farmers?id=eq.${farmer.id}`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), Prefer: 'return=minimal' },
      body: JSON.stringify({ metadata: { ...(farmer.metadata || {}), grainStorage: next } }),
    }).catch(() => {});
  }

  function chooseMode(next) {
    setMode(next);
    setSaving(true);
    const cap = next === STORAGE_MODE.HAS_STORAGE ? (parseFloat(capacityInput.replace(/,/g, '')) || null) : null;
    persist(next, cap);
    setSaving(false);
  }

  function commitCapacity() {
    if (mode !== STORAGE_MODE.HAS_STORAGE) return;
    const cap = parseFloat(capacityInput.replace(/,/g, '')) || null;
    persist(mode, cap);
  }

  return (
    <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
      <div style={{ ...uiLabel, marginBottom: 6 }}>Storage strategy</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {[[STORAGE_MODE.DELIVER_AT_HARVEST, 'Deliver at Harvest'], [STORAGE_MODE.HAS_STORAGE, 'Has On-Farm Storage']].map(([val, label]) => (
          <button key={val} onClick={() => chooseMode(val)} disabled={saving}
            style={{ padding: '6px 12px', borderRadius: T.r.pill, border: `1.5px solid ${mode === val ? T.green : T.lineStrong}`,
              background: mode === val ? T.green : '#fff', color: mode === val ? '#fff' : T.body, fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>
            {label}
          </button>
        ))}
        {mode === STORAGE_MODE.HAS_STORAGE && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.body }}>
            capacity
            <input type="text" inputMode="numeric" value={capacityInput} placeholder="bu (optional)"
              onChange={ev => setCapacityInput(ev.target.value)} onBlur={commitCapacity}
              style={{ width: 90, padding: '5px 8px', borderRadius: T.r.sm, border: `1px solid ${T.lineStrong}`, fontSize: 12, ...uiNum }} />
            bu
          </span>
        )}
      </div>
      {!mode && (
        <div style={{ fontSize: 10.5, color: T.faint, marginTop: 4 }}>
          Not yet set — once set, this distinguishes harvest-delivery pressure from storable bushels below (it does not move the futures gauge or the bushel sizing math).
        </div>
      )}
      {/* Storage exposure split (Kevin, 2026-08-18 21:45, extended 2026-08-19
          mobile review: "storage capacity must become position-relevant...
          it should distinguish harvest-delivery pressure from storable
          bushels"). Pure mechanical arithmetic (deriveStorageExposure,
          grainStorage.js) — it never changes status/sizing/the futures or
          basis gauges, but the harvest-delivery-required figure now also
          surfaces as an action reason code (grainAction.js) so it is
          guidance, not just a stored fact. Unknown expected crop or unknown
          capacity render an explicit "not known" line rather than a
          fabricated 0-bu split. */}
      {storage?.exposure && (
        storage.exposure.known ? (
          <div style={{ marginTop: 8 }}
            title={`${fmt(storage.exposure.harvestDeliveryRequiredBu, 0)} bu must move at harvest (${storage.exposure.harvestDeliveryRequiredPct}%) · `
              + `${fmt(storage.exposure.storableBu, 0)} bu storable (${storage.exposure.storablePct}%) of ${fmt(storage.exposure.expectedCropBu, 0)} bu expected`}>
            <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: T.surfaceAlt }}>
              <div style={{ width: `${storage.exposure.harvestDeliveryRequiredPct}%`, background: '#b08a2a' }} />
              <div style={{ width: `${storage.exposure.storablePct}%`, background: '#2d6a1a' }} />
            </div>
            <div style={{ fontSize: 10.5, color: T.faint, marginTop: 2 }}>
              <b style={{ color: '#b08a2a' }}>{fmt(storage.exposure.harvestDeliveryRequiredBu, 0)}</b> move at harvest · <b style={{ color: '#2d6a1a' }}>{fmt(storage.exposure.storableBu, 0)}</b> storable
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 10.5, color: T.faint, marginTop: 8 }}>
            Harvest-delivery-required vs. storable split: not known yet ({storage.exposure.expectedCropBu == null ? 'expected crop unknown' : mode === STORAGE_MODE.HAS_STORAGE ? 'storage capacity not stated' : 'storage strategy not set'}).
          </div>
        )
      )}
    </div>
  );
}



// ── GrainCashFlowChart — money by delivery month, from sales already made.
//
// Three stacked states per bar, stacked rather than summed because they are
// not the same claim:
//   solid    KNOWN     a cash price is set (or futures AND basis both are)
//   hatched  ESTIMATE  futures locked, basis open, valued at an OBSERVED bid
//   dashed   BUSHELS   committed with no price at all — no dollars, ever
//
// Without that split Meisenzahl's book renders 35,000 bushels of open basis as
// settled money. With it the chart shows what is banked, what is still exposed
// to basis, and what has no price yet — which is the actual question a
// cash-flow plan asks. See tools/check-grain-cashflow.js.
function GrainCashFlowChart({ flow, isMobile }) {
  const months = flow?.months || [];
  if (!months.length) {
    return (
      <div style={{ fontSize: 12, color: '#9bb59b', fontStyle: 'italic', padding: '12px 0' }}>
        No dated contracts yet — a cash-flow month needs a delivery window on the contract.
        {flow?.undatedBu > 0 ? ' ' + fmt(flow.undatedBu, 0) + ' bu are contracted with no delivery dates.' : ''}
      </div>
    );
  }
  // Bars scale on DOLLARS. A bushels-only month has none, so it draws as a
  // short dashed marker rather than competing on the money scale — a tall bar
  // for bushels with no price would read as cash.
  const maxD = Math.max(1, ...months.map(m => m.total));
  const H = 130, barW = isMobile ? 26 : 34, gap = isMobile ? 8 : 12;
  const W = months.length * (barW + gap) + gap;
  const hasUnpriced = months.some(m => m.unpricedBu > 0);
  const sw = { display: 'inline-block', width: 9, height: 9, borderRadius: 2, marginRight: 4 };
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <svg width={Math.max(W, 220)} height={H + 44} style={{ display: 'block' }}>
          <defs>
            <pattern id="cfHatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="5" height="5" fill="#eef3ec" />
              <line x1="0" y1="0" x2="0" y2="5" stroke="#5a8f3a" strokeWidth="2.5" />
            </pattern>
          </defs>
          {months.map((m, i) => {
            const x = gap + i * (barW + gap);
            const kh = Math.round((m.known / maxD) * H);
            const eh = Math.round((m.estimated / maxD) * H);
            const only = m.total === 0 && m.unpricedBu > 0;
            const parts = [m.label];
            if (m.known > 0) parts.push('priced $' + fmt(Math.round(m.known), 0) + ' (' + fmt(Math.round(m.knownBu), 0) + ' bu)');
            if (m.estimated > 0) parts.push('basis open ~$' + fmt(Math.round(m.estimated), 0) + ' (' + fmt(Math.round(m.estimatedBu), 0) + ' bu)');
            if (m.unpricedBu > 0) parts.push(fmt(Math.round(m.unpricedBu), 0) + ' bu committed, no price set');
            return (
              <g key={m.key}>
                <title>{parts.join(' · ')}</title>
                {only ? (
                  <rect x={x} y={H - 14} width={barW} height={14} fill="none" stroke="#b9c9b9" strokeWidth="1.5" strokeDasharray="3 2" rx="2" />
                ) : (
                  <>
                    <rect x={x} y={H - kh} width={barW} height={kh} fill="#2d6a1a" rx="2" />
                    <rect x={x} y={H - kh - eh} width={barW} height={eh} fill="url(#cfHatch)" stroke="#5a8f3a" strokeWidth="1" rx="2" />
                  </>
                )}
                {m.unpricedBu > 0 && !only && (
                  <rect x={x} y={H - kh - eh - 9} width={barW} height={7} fill="none" stroke="#b9c9b9" strokeWidth="1.2" strokeDasharray="3 2" rx="2" />
                )}
                <text x={x + barW / 2} y={H + 14} textAnchor="middle" fontSize="9.5" fill="#60735f">{m.label}</text>
                {m.total > 0 && (
                  <text x={x + barW / 2} y={H + 28} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#1a2e1a">
                    {m.total >= 1000 ? Math.round(m.total / 1000) + 'k' : Math.round(m.total)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span><span style={{ ...sw, background: '#2d6a1a' }} />priced ${fmt(Math.round(flow.totalKnown), 0)}</span>
        {flow.totalEstimated > 0 && (
          <span title={'Hatched = futures locked, basis NOT set. The cash is an estimate at today observed basis' + (flow.basisRefLabel ? ' (' + flow.basisRefLabel + ')' : '') + ', not a settled price.'}><span style={{ ...sw, background: 'repeating-linear-gradient(45deg,#5a8f3a 0 2px,#eef3ec 2px 5px)', border: '1px solid #5a8f3a' }} />hatched = basis not set ~${fmt(Math.round(flow.totalEstimated), 0)}</span>
        )}
        {hasUnpriced && (
          <span><span style={{ ...sw, border: '1.2px dashed #b9c9b9' }} />{fmt(Math.round(flow.unpricedBu), 0)} bu no price set</span>
        )}
        {flow.undatedBu > 0 && <span>{fmt(Math.round(flow.undatedBu), 0)} bu undated</span>}
      </div>
    </div>
  );
}



// GRAIN_DECISION_COLOR removed 2026-09-01 with the decision engine it coloured.







// ═══════════════════════════════════════════════════════════════════════════
// DrpFarmerSelector — same picker shape as GrainFarmerSelector above, adapted
// to DRP: the activity badge counts drp_endorsements rows instead of
// grain_contracts, and the header/labels are DRP's. Kept as its own small
// component rather than generalizing GrainFarmerSelector — that would touch
// a function another concurrent Silas build (§1.2c) just landed, and this
// repo's own working conventions (AGENTS.md §7) prefer a new, isolated
// component to a shared edit in an 85k-line file with other lanes active.
function DrpFarmerSelector({ farmerList, isMobile, onSelect, cardStyle }) {
  const [search, setSearch] = useState('');
  const [endCounts, setEndCounts] = useState({});

  const uniqueFarmers = useMemo(() => {
    const byName = new Map();
    for (const f of farmerList) {
      const key = (f.name || '').trim().toLowerCase();
      if (!key) continue;
      const existing = byName.get(key);
      if (!existing) { byName.set(key, f); continue; }
      const fPolicies = (f.allFarmerDbIds?.length || 0) + (f._allPolicyNums?.length || 0);
      const ePolicies = (existing.allFarmerDbIds?.length || 0) + (existing._allPolicyNums?.length || 0);
      if (fPolicies > ePolicies) byName.set(key, f);
    }
    return Array.from(byName.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [farmerList]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const farmerIds = uniqueFarmers.flatMap(f => f.allFarmerDbIds || [f.id].filter(Boolean));
        if (!farmerIds.length) return;
        const pols = await sbFetch('policies', { select: 'id,farmer_id', filters: [`farmer_id=in.(${farmerIds.join(',')})`] }).catch(() => []);
        const farmerByPolicy = {};
        for (const p of (pols || [])) farmerByPolicy[p.id] = p.farmer_id;
        const pids = Object.keys(farmerByPolicy);
        if (!pids.length) return;
        const rows = await sbFetch('drp_endorsements', { select: 'policy_id', filters: [`policy_id=in.(${pids.join(',')})`] }).catch(() => []);
        if (cancelled) return;
        const counts = {};
        for (const r of (rows || [])) {
          const fid = farmerByPolicy[r.policy_id];
          if (fid) counts[fid] = (counts[fid] || 0) + 1;
        }
        setEndCounts(counts);
      } catch {/* table may not exist yet — silent, same as GrainFarmerSelector */}
    }
    load();
    return () => { cancelled = true; };
  }, [uniqueFarmers]);

  const q = search.trim().toLowerCase();
  const filtered = q ? uniqueFarmers.filter(f => (f.name || '').toLowerCase().includes(q)) : uniqueFarmers;
  const farmerEndCount = (f) => (f.allFarmerDbIds || [f.id].filter(Boolean)).reduce((s, id) => s + (endCounts[id] || 0), 0);
  const withEnds = filtered.filter(f => farmerEndCount(f) > 0).sort((a, b) => farmerEndCount(b) - farmerEndCount(a) || (a.name || '').localeCompare(b.name || ''));
  const withoutEnds = filtered.filter(f => farmerEndCount(f) === 0);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>Select a farm</div>
        <div style={{ fontSize: 12.5, color: T.muted, marginTop: 2 }}>Dairy Revenue Protection exposure and existing coverage, per quarter.</div>
      </div>
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 280px', position: 'relative' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search farmers…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px 10px 38px', border: '1px solid #c8d8c0', borderRadius: 9, fontSize: 14, background: '#fff', color: '#1a2e1a', fontFamily: 'inherit', outline: 'none' }} />
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#60735f', fontSize: 14 }}>🔎</span>
        </div>
        <div style={{ fontSize: 11, color: '#60735f' }}>{filtered.length} of {uniqueFarmers.length} {uniqueFarmers.length === 1 ? 'farm' : 'farms'}</div>
      </div>
      {(() => {
        const gridStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 };
        const sectionLbl = { fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2d4a2d', margin: '4px 2px 9px' };
        const renderCard = (f) => {
          const count = farmerEndCount(f);
          const counties = f._allCounties || (f.county ? [f.county] : []);
          return (
            <button key={f.id} onClick={() => onSelect(f)}
              style={{ ...cardStyle, cursor: 'pointer', textAlign: 'left', display: 'block', width: '100%', marginBottom: 0, position: 'relative', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#2d4a2d'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(45,74,45,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#dde8d8'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1a2e1a', fontFamily: "'Lora',serif", flex: 1 }}>{f.name}</div>
                {count > 0 && <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 12, background: '#eaf4e4', color: '#2d5a10', whiteSpace: 'nowrap' }}>{count} endorsement{count === 1 ? '' : 's'}</span>}
              </div>
              <div style={{ fontSize: 11, color: '#60735f', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {counties.length > 0 && <div>{counties.slice(0, 3).join(' · ')}{counties.length > 3 ? ` +${counties.length - 3}` : ''}</div>}
                {count === 0 && <span style={{ fontSize: 10, color: '#a8b8a8', fontStyle: 'italic' }}>no DRP on file</span>}
              </div>
            </button>
          );
        };
        if (filtered.length === 0) return <div style={{ ...cardStyle, textAlign: 'center', color: '#60735f' }}>{q ? `No farmers match "${q}"` : 'No farmers on file.'}</div>;
        return (
          <>
            {withEnds.length > 0 && <div style={{ marginBottom: 20 }}><div style={sectionLbl}>🥛 Has DRP · {withEnds.length}</div><div style={gridStyle}>{withEnds.map(renderCard)}</div></div>}
            {q ? (
              <div>{withEnds.length > 0 && <div style={sectionLbl}>Other Matches · {withoutEnds.length}</div>}<div style={gridStyle}>{withoutEnds.map(renderCard)}</div></div>
            ) : (
              withoutEnds.length > 0 && <div style={{ ...cardStyle, textAlign: 'center', color: '#60735f', fontSize: 13 }}>🔎 {withoutEnds.length} more {withoutEnds.length === 1 ? 'farm' : 'farms'} without DRP — type a name above to pull one up.</div>
            )}
          </>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
function GrainContractModal({ form: initialForm, editing, saving, getFutures, cropYear, knownLocations, onClose, onSave, onDelete }) {
  // The form holds the structured fields the new schema uses. Legacy
  // shape (`bushels`, `futures`, `fmonth`, `type`) is normalized on load
  // so editing works for old rows too.
  const initialMonthName = initialForm.futures_month_name
    || (initialForm.fmonth ? initialForm.fmonth.split(' ')[0] : null)
    || (initialForm.crop ? FUTURES_MONTHS_BY_CROP[initialForm.crop]?.[0] : null);
  const initialMonthYear = initialForm.futures_month_year
    || (initialForm.fmonth && initialForm.fmonth.match(/(\d{2,4})$/)
        ? (initialForm.fmonth.match(/(\d{2,4})$/)[1].length === 2
            ? 2000 + parseInt(initialForm.fmonth.match(/(\d{2,4})$/)[1], 10)
            : parseInt(initialForm.fmonth.match(/(\d{2,4})$/)[1], 10))
        : (cropYear || new Date().getFullYear()));
  const [form, setForm] = useState({
    contract_number: initialForm.contract_number || '',
    pricing_line_id: initialForm.pricing_line_id || '',
    crop: initialForm.crop || 'Corn',
    // WHICH PRACTICE WILL FILL THIS CONTRACT. '' means NOT STATED, and that is
    // the default on purpose: an elevator file never says, so the form must not
    // put a word in the grower's mouth. On a farm insuring one practice for the
    // commodity the app derives it from the coverage and this can stay blank;
    // on a farm insuring both it is the only thing that can place the bushels.
    practice: initialForm.practice || '',
    contract_type: initialForm.contract_type || initialForm.type || 'Fixed Price',
    buyer: initialForm.buyer || '',
    location: initialForm.location || '',
    bushels_contracted: initialForm.bushels_contracted ?? initialForm.bushels ?? '',
    bushels_priced: initialForm.bushels_priced ?? '',
    bushels_delivered: initialForm.bushels_delivered ?? '',
    futures_price: initialForm.futures_price ?? initialForm.futures ?? '',
    basis: initialForm.basis ?? '',
    cash_price: initialForm.cash_price ?? '',
    futures_month_name: initialMonthName,
    futures_month_year: initialMonthYear,
    ship_month: initialForm.ship_month || '',
    contract_date: initialForm.contract_date || '',
    delivery_start: initialForm.delivery_start || initialForm.dstart || '',
    delivery_end: initialForm.delivery_end || initialForm.dend || '',
    notes: initialForm.notes || '',
    id: initialForm.id,
  });

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  // When crop changes, ensure the futures_month_name is one this crop
  // actually trades (e.g. switching Soybeans → Corn drops Aug from the
  // valid month list — reset to first available).
  function setCrop(crop) {
    setForm(f => {
      const validMonths = FUTURES_MONTHS_BY_CROP[crop] || FUTURES_MONTHS_BY_CROP.Corn;
      const monthName = validMonths.includes(f.futures_month_name) ? f.futures_month_name : validMonths[0];
      const refPrice = (() => {
        try { return getFutures(crop)?.price; } catch { return null; }
      })();
      // ALWAYS refresh the futures price to the new crop's board — a futures price is
      // crop-specific, so a value carried over from the previously-selected crop (e.g.
      // the corn price the new-contract form pre-fills) is never valid for the new crop.
      // Keeping it stale is the "soybean contract priced off corn (4.56)" bug. If the ref
      // can't resolve, blank it so the user types the real price rather than inherit a lie.
      return { ...f, crop, futures_month_name: monthName, futures_price: refPrice != null ? refPrice : '' };
    });
  }

  // When contract type changes, gray out fields that don't apply.
  function setType(newType) {
    setForm(f => {
      const next = { ...f, contract_type: newType };
      // Type-specific zeroing — nothing destructive, just clearing
      // values that don't apply so they don't leak through to save.
      if (newType === 'Basis')        next.futures_price = '';
      if (newType === 'HTA')          next.basis = '';
      if (newType === 'Bushels Only') { next.futures_price = ''; next.basis = ''; next.cash_price = ''; }
      return next;
    });
  }

  // Auto-derive cash price for Fixed Price contracts as user fills futures + basis
  const computedCash = (() => {
    if (form.contract_type !== 'Fixed Price') return null;
    const f = parseFloat(form.futures_price), b = parseFloat(form.basis);
    if (isNaN(f) || isNaN(b)) return null;
    return (f + b).toFixed(2);
  })();

  // Build the payload the parent's onSave expects. Translates the
  // structured fields back into both the new-schema and legacy-shape
  // keys so existing callers (including the in-memory contract list)
  // keep working.
  function handleSave() {
    if (!form.crop || !form.contract_type) {
      alert('Crop and contract type are required.');
      return;
    }
    const monthDisplay = fmonthDisplay(form.futures_month_name, form.futures_month_year);
    const futuresSymbol = buildFuturesSymbol(form.crop, form.futures_month_name, form.futures_month_year);
    const num = (v) => (v === '' || v == null ? null : Number(v));
    const cashPriceFinal = form.contract_type === 'Fixed Price' && computedCash != null
      ? Number(computedCash)
      : num(form.cash_price);
    onSave({
      id: form.id,
      contract_number: form.contract_number?.trim() || null,
      pricing_line_id: form.pricing_line_id?.trim() || null,
      crop: form.crop,
      // '' -> null: the column is nullable and NULL is the honest "not stated".
      // Writing an empty string would make a blank look like a stated value to
      // any query that tests the column for presence.
      practice: form.practice || null,
      contract_type: form.contract_type,
      // legacy keys for backwards-compat with anything reading the in-memory list
      type: form.contract_type,
      bushels: num(form.bushels_contracted),
      futures: num(form.futures_price),
      fmonth: monthDisplay,
      // new schema keys
      bushels_contracted: num(form.bushels_contracted),
      bushels_priced: num(form.bushels_priced),
      bushels_delivered: num(form.bushels_delivered),
      futures_price: num(form.futures_price),
      basis: num(form.basis),
      cash_price: cashPriceFinal,
      futures_symbol: futuresSymbol,
      futures_month: monthDisplay,
      futures_month_name: form.futures_month_name,
      ship_month: form.ship_month || null,
      futures_month_year: form.futures_month_year,
      buyer: form.buyer || null,
      location: form.location || null,
      contract_date: form.contract_date || null,
      delivery_start: form.delivery_start || null,
      delivery_end: form.delivery_end || null,
      dstart: form.delivery_start || null,
      dend: form.delivery_end || null,
      notes: form.notes || null,
      source: 'manual',
    });
  }

  // Suggested year range for the futures-month dropdown — current cropYear ± 2
  const yearOptions = (() => {
    const base = cropYear || new Date().getFullYear();
    return [base - 1, base, base + 1, base + 2];
  })();

  const validMonths = FUTURES_MONTHS_BY_CROP[form.crop] || FUTURES_MONTHS_BY_CROP.Corn;

  const ov = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, paddingTop: 30, overflowY: 'auto' };
  const ms = { background: '#fff', borderRadius: 12, padding: '24px 28px', width: 560, maxWidth: '96vw', marginBottom: 40, fontFamily: "'Plus Jakarta Sans','Helvetica Neue',sans-serif" };
  const inp = { width: '100%', padding: '8px 11px', border: '1px solid #c8d8c0', borderRadius: 7, fontSize: 13, background: '#fff', color: '#1a2e1a', fontFamily: 'inherit', boxSizing: 'border-box' };
  const inpMuted = { ...inp, background: '#f6faf4', color: '#a8b8a8' };
  const lbl = { display: 'block', fontSize: 10, fontWeight: 800, color: '#4a6e4a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 };

  const showFutures = form.contract_type !== 'Basis' && form.contract_type !== 'Bushels Only';
  const showBasis   = form.contract_type !== 'HTA' && form.contract_type !== 'Bushels Only';
  const showCash    = form.contract_type === 'Fixed Price';

  return (
    <div style={ov} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={ms}>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#1a2e1a', marginBottom: 4 }}>{editing ? 'Edit' : 'Add'} Contract</div>
        <div style={{ fontSize: 11, color: '#60735f', marginBottom: 18, fontStyle: 'italic' }}>
          {GRAIN_TYPE_DESCRIPTIONS[form.contract_type] || ''}
        </div>

        {/* Contract # + crop + type */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Contract #</label>
            <input value={form.contract_number} onChange={e => set('contract_number', e.target.value)} placeholder="102200 or S0030998P" style={inp} />
          </div>
          <div>
            <label style={lbl}>Pricing Line (optional)</label>
            <input value={form.pricing_line_id} onChange={e => set('pricing_line_id', e.target.value)} placeholder="P00 / P01 / P02" style={inp} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Crop</label>
            <select value={form.crop} onChange={e => setCrop(e.target.value)} style={inp}>
              {GRAIN_CROPS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Practice</label>
            <select value={form.practice} onChange={(e) => set('practice', e.target.value)} style={inp}>
              <option value="">Not stated</option>
              <option value="conventional">Conventional</option>
              <option value="organic">Organic</option>
            </select>
            <div style={{ fontSize: 10.5, color: '#60735f', marginTop: 3 }}>
              Only needed where the farm insures BOTH practices for this crop — otherwise it is read from the coverage.
            </div>
          </div>
          <div>
            <label style={lbl}>Contract Type</label>
            <select value={form.contract_type} onChange={e => setType(e.target.value)} style={inp}>
              {GRAIN_CONTRACT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Buyer + WHERE IT GOES. These are two different facts and a contract
            routinely carries two different companies: Jeffres sells wheat to
            Howlett Farms delivered to Star of the West. The field existed in
            this form's state and was submitted, but was never RENDERED — so
            17 of the 20 hand-entered contracts in the book carry no delivery
            point at all, against 0 of 621 imported ones. Basis is posted per
            delivery point, so those 17 cannot be checked against any bid. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Buyer / Elevator</label>
            <input value={form.buyer} onChange={e => set('buyer', e.target.value)} placeholder="e.g. Lima Elevator" style={inp} />
          </div>
          <div>
            <label style={lbl}>Delivery Location</label>
            <input list="grain-contract-locations" value={form.location} onChange={e => set('location', e.target.value)}
              placeholder="e.g. Star of the West" style={inp}
              title="Where the grain is delivered — often the buyer's own yard, but not always. Basis is posted per delivery point, so this is what lets the contract be checked against the bid it was written off." />
            <datalist id="grain-contract-locations">{(knownLocations || []).map(l => <option key={l} value={l} />)}</datalist>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={lbl}>Bushels Contracted</label>
            <input type="number" value={form.bushels_contracted} onChange={e => set('bushels_contracted', e.target.value)} min="0" placeholder="0" style={inp} />
          </div>
          <div>
            <label style={lbl}>Bushels Priced</label>
            <input type="number" value={form.bushels_priced} onChange={e => set('bushels_priced', e.target.value)} min="0" placeholder="0" style={inp} />
          </div>
          <div>
            <label style={lbl}>Bushels Delivered</label>
            <input type="number" value={form.bushels_delivered} onChange={e => set('bushels_delivered', e.target.value)} min="0" placeholder="0" style={inp} />
          </div>
        </div>

        {/* Futures month + ship month. The futures-contract month (e.g., ZCZ26
            = Dec 26 corn) is the pricing reference. The ship month is when
            the buyer wants the bushels delivered — often a different month
            (e.g., priced off Dec board, ship in Oct or Nov). */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: 12, marginBottom: 4 }}>
          <div>
            <label style={lbl}>Futures Month</label>
            <select value={form.futures_month_name || ''} onChange={e => set('futures_month_name', e.target.value)} style={inp}>
              {validMonths.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Year</label>
            <select value={form.futures_month_year || ''} onChange={e => set('futures_month_year', parseInt(e.target.value, 10))} style={inp}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl} title="Actual delivery month — can differ from the futures contract month.">Ship Month</label>
            <select value={form.ship_month || ''} onChange={e => set('ship_month', e.target.value)} style={inp}>
              <option value="">—</option>
              {(() => {
                // Show 18 months centered on cropYear so the user can pick
                // anything from harvest of prior year through delivery of next
                const start = (form.futures_month_year || cropYear || new Date().getFullYear()) - 1;
                const opts = [];
                for (let yr = start; yr <= start + 2; yr++) {
                  for (const m of ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']) {
                    opts.push(`${m} ${String(yr).slice(-2)}`);
                  }
                }
                return opts.map(o => <option key={o} value={o}>{o}</option>);
              })()}
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 14, fontSize: 11, color: '#60735f', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          {form.crop && form.futures_month_name && form.futures_month_year ? (
            <span>Futures symbol: <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: '#1a2e1a' }}>{buildFuturesSymbol(form.crop, form.futures_month_name, form.futures_month_year)}</span></span>
          ) : <span />}
          <span style={{ fontStyle: 'italic' }}>Ship month is when the buyer wants delivery — may differ from the futures month.</span>
        </div>

        {/* Pricing fields — conditional on type */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div style={{ opacity: showFutures ? 1 : 0.4 }}>
            <label style={lbl}>Futures Price</label>
            <input type="number" step="0.0025" value={form.futures_price ?? ''} onChange={e => set('futures_price', e.target.value)} placeholder="0.00" style={showFutures ? inp : inpMuted} disabled={!showFutures} />
          </div>
          <div style={{ opacity: showBasis ? 1 : 0.4 }}>
            <label style={lbl}>Basis</label>
            <input type="number" step="0.01" value={form.basis ?? ''} onChange={e => set('basis', e.target.value)} placeholder="-0.25" style={showBasis ? inp : inpMuted} disabled={!showBasis} />
          </div>
          <div style={{ opacity: showCash ? 1 : 0.4 }}>
            <label style={lbl}>Cash Price{computedCash != null && <span style={{ marginLeft: 4, color: '#60735f', fontWeight: 500, textTransform: 'none' }}>(auto)</span>}</label>
            <input type="number" step="0.01"
              value={computedCash != null ? computedCash : (form.cash_price ?? '')}
              onChange={e => set('cash_price', e.target.value)}
              placeholder="0.00"
              style={showCash ? inp : inpMuted}
              disabled={!showCash || computedCash != null}
            />
          </div>
        </div>

        {/* Dates */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div><label style={lbl}>Contract Date</label><input type="date" value={form.contract_date || ''} onChange={e => set('contract_date', e.target.value)} style={inp} /></div>
          <div><label style={lbl}>Delivery Start</label><input type="date" value={form.delivery_start || ''} onChange={e => set('delivery_start', e.target.value)} style={inp} /></div>
          <div><label style={lbl}>Delivery End</label><input type="date" value={form.delivery_end || ''} onChange={e => set('delivery_end', e.target.value)} style={inp} /></div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>Notes</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>{onDelete && <button onClick={onDelete} style={{ padding: '6px 12px', background: '#fdecea', color: '#c0392b', border: '1px solid #e0a0a0', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Delete</button>}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 16px', background: '#fff', color: '#4a6e4a', border: '1px solid #c8d8c0', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', background: '#2d4a2d', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save Contract'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GrainFuturesModal({ futuresRef, cropYear, saving, onClose, onSave }) {
  const init = {};
  GRAIN_CROPS.forEach(crop => { const row = futuresRef.find(r => r.crop === crop); init[crop] = { price: row?.price || GRAIN_DEFAULT_FUTURES[cropYear]?.[crop]?.price || 4.50, month: row?.ref_month || '' }; });
  const [vals, setVals] = useState(init);
  const ov = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const ms = { background: '#fff', borderRadius: 12, padding: '22px 26px', width: 400, maxWidth: '95vw', fontFamily: "'Plus Jakarta Sans','Helvetica Neue',sans-serif" };
  const inp = { width: '100%', padding: '8px 11px', border: '1px solid #c8d8c0', borderRadius: 7, fontSize: 13, background: '#fff', color: '#1a2e1a', fontFamily: 'inherit' };
  const lbl = { display: 'block', fontSize: 10, fontWeight: 800, color: '#4a6e4a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 };
  return (
    <div style={ov} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={ms}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a2e1a', marginBottom: 6 }}>Update Futures Prices</div>
        <div style={{ fontSize: 11, color: '#60735f', marginBottom: 18 }}>Reference nearby prices for {cropYear}. Basis contracts reprice automatically.</div>
        {GRAIN_CROPS.map(crop => (
          <div key={crop} style={{ marginBottom: 14 }}>
            <label style={{ ...lbl, color: GRAIN_COLORS[crop] }}>{crop}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input type="number" step="0.01" value={vals[crop].price} onChange={e => setVals(v => ({ ...v, [crop]: { ...v[crop], price: parseFloat(e.target.value) || 0 } }))} placeholder="Price" style={inp} />
              <input value={vals[crop].month} onChange={e => setVals(v => ({ ...v, [crop]: { ...v[crop], month: e.target.value } }))} placeholder="e.g. Dec 26" style={inp} />
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#fff', color: '#4a6e4a', border: '1px solid #c8d8c0', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onSave(GRAIN_CROPS.map(crop => ({ crop, price: vals[crop].price, month: vals[crop].month })))} disabled={saving}
            style={{ padding: '8px 16px', background: '#2d4a2d', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Update Prices'}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FSA-426 generator — lives inside FSA578Screen's own tab.
//
// Kevin, 2026-09-23: *"The 426 is a report we send to FSA to request 578s… Make sure 426
// generator can be an Individual farm, multiple selected farms, or all the farms that
// operate in that county… Also need to be able to filter by crop… We will generally always
// check box request for 578 producer print but occasionally do request map prints so allow
// us to select."*
//
// ⚠ THE FORM IS ADDRESSED TO A COUNTY OFFICE, SO THE COUNTY IS THE UNIT, NOT THE FARM.
// A grower who farms in three counties is on three forms — which is what the six Livingston
// pages we filed in March already show. "All the farms that operate in that county" is
// therefore the DEFAULT here, and picking individual farms narrows within that.
//
// ⚠ NOTHING ON THIS SCREEN HOLDS MORE THAN FOUR DIGITS OF A TAX ID. The payload carries
// `idLast4` and a type code and nothing else; there is no full value to render, redact or
// leak, because `api/_taxid4.js` never returns one.
// ─────────────────────────────────────────────────────────────────────────────
// ── SAVING A SIGNATURE FOR ITEM 11A ──────────────────────────────────────────
//
// Kevin, 2026-09-30: *"For 426 add a signature step, or allow me to sign and save my
// signature in Silas somehow."*
//
// What is captured and stored is the PEN MOVEMENT — strokes of points — not an image.
// `api/_fsa426-signature.js` explains why at length; the short version is that
// `_fsa426-pdf.js` appends an incremental update to FSA's own rendered page and already
// draws one non-text thing (a ticked checkbox) as PATH operators, so a signature is the
// same kind of object and needs no new PDF machinery. An image would have needed four new
// mechanisms and would have broken the assertion that FSA's own bytes are untouched.
//
// ⚠ THE CANVAS IS SHAPED LIKE THE SIGNATURE LINE, AND THAT IS NOT COSMETIC. Item 11A is
// 273 x 18pt — about 15:1. A signature drawn in a square box and fitted into that line is
// scaled down by its HEIGHT and lands as a small mark in the middle of a long line. Keeping
// the capture box wide and short means what you draw is what prints.
function Fsa426RequesterBlock() {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [idNum, setIdNum] = useState("");
  const [idType, setIdType] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    try {
      const r = await adminApiFetch("/api/market?fn=fsa426&op=requester");
      const j = await apiJson(r, "the requester service");
      setData(j);
      if (j && j.requester) {
        setName(j.requester.name || ""); setIdNum(j.requester.id || ""); setIdType(j.requester.idType || "");
      }
    } catch (e) { setErr(e.message); setData({ onFile: false }); }
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      const r = await adminApiFetch("/api/market?fn=fsa426&op=requester", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, id: idNum, idType }),
      });
      await apiJson(r, "the requester service");
      setMsg("Saved — this is what will print in items 4A, 4C and 4D.");
      await refresh();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const card = { background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: 16, marginBottom: 14 };
  const lab = { fontSize: 11, fontWeight: 700, color: "#5a6a5a", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 3 };
  const inp = { padding: "7px 9px", borderRadius: 7, border: "1px solid #c8d8c8", fontSize: 13, width: "100%", boxSizing: "border-box" };
  const w = (data && data.widths) || { name: 41, id: 11, idType: 3 };

  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", marginBottom: 4 }}>
        Who is asking — items 4A, 4C and 4D
      </div>
      <div style={{ fontSize: 12, color: "#5a6a5a", marginBottom: 10, lineHeight: 1.5 }}>
        This is yours alone, saved against the login you are signed in as, so a form you
        generate carries your name and licence over your own signature.
        {data && !data.onFile ? (
          <b style={{ color: "#a9791f" }}> Nothing is saved yet — a 426 will not generate until it is.</b>
        ) : null}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ flex: "2 1 220px" }}>
          <div style={lab}>Requester name (4A)</div>
          <input style={inp} value={name} maxLength={w.name} onChange={(e) => setName(e.target.value)} placeholder="Daniele Harris" />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <div style={lab}>ID number (4C)</div>
          <input style={inp} value={idNum} maxLength={w.id} onChange={(e) => setIdNum(e.target.value)} placeholder="2071" />
        </div>
        <div style={{ flex: "0 1 90px" }}>
          <div style={lab}>ID type (4D)</div>
          <input style={inp} value={idType} maxLength={w.idType} onChange={(e) => setIdType(e.target.value.toUpperCase())} placeholder="LIC" />
        </div>
      </div>
      {/* NOT EDITABLE, and shown so it is obvious they are not yours to change. 11B is
          built from 4C so the title can never disagree with the licence beside it. */}
      <div style={{ fontSize: 11.5, color: "#6a7a6a", marginBottom: 10, lineHeight: 1.6 }}>
        Shared by the agency, printed as-is: phone <b>{(data && data.sharedPhone) || "—"}</b>
        {" "}· reply-to <b>{(data && data.sharedEmail) || "—"}</b>
        {idNum ? <> · title (11B) <b>{"Agent - License #" + idNum}</b></> : null}
      </div>
      <button style={{ padding: "7px 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 700,
        cursor: busy ? "default" : "pointer", border: "none", background: "#2d4a2d", color: "#fff", opacity: busy ? 0.6 : 1 }}
        disabled={busy} onClick={save}>{busy ? "Saving…" : "Save my details"}</button>
      {msg ? <span style={{ marginLeft: 10, fontSize: 12, color: "#1f7a3d" }}>{msg}</span> : null}
      {err ? <span style={{ marginLeft: 10, fontSize: 12, color: "#c0392b" }}>{err}</span> : null}
    </div>
  );
}

function Fsa426SignatureCapture() {
  const isMobile = useIsMobile();
  const canvasRef = useRef(null);
  const [strokes, setStrokes] = useState([]);      // [[{x,y}…]…] normalized 0..1
  const drawing = useRef(false);
  const [onFile, setOnFile] = useState(null);      // null = not loaded yet
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  // 7.5:1 — wide and short, so a natural signature maps onto the 15:1 line without being
  // shrunk to fit. Not 15:1 because nobody can sign in an 18-pixel-tall strip.
  const W = isMobile ? 320 : 600, H = Math.round((isMobile ? 320 : 600) / 7.5);

  const refresh = async () => {
    try {
      const r = await adminApiFetch("/api/market?fn=fsa426&op=signature");
      const j = await apiJson(r, "the signature service");
      setOnFile(j);
      if (j && j.signature && j.signature.signer_name) setName(j.signature.signer_name);
      else if (j && j.signer) setName((prev) => prev);
    } catch (e) { setErr(e.message); setOnFile({ onFile: false }); }
  };
  useEffect(() => { refresh(); }, []);   // eslint-disable-line

  // ⚠ POINTER EVENTS, NOT MOUSE EVENTS. Kevin signs on a phone as often as a desktop, and
  // mouse handlers do not fire for touch. `setPointerCapture` keeps the stroke alive when
  // the finger leaves the canvas, which otherwise ends a stroke mid-letter.
  const pt = (e) => {
    const c = canvasRef.current; if (!c) return null;
    const r = c.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
    // Clamp HERE, at the edge of the canvas, where a value slightly outside is a finger
    // on the border rather than bad data. The server refuses out-of-range rather than
    // clamping, which is right there and wrong here.
    return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };
  };
  const down = (e) => {
    const p = pt(e); if (!p) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    setStrokes((s) => [...s, [p]]);
    setMsg(null); setErr(null);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pt(e); if (!p) return;
    setStrokes((s) => { if (!s.length) return s;
      const last = s[s.length - 1]; return [...s.slice(0, -1), [...last, p]]; });
  };
  const up = () => { drawing.current = false; };

  // Repaint on every change. A canvas is not React state, so this effect IS the render.
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const g = c.getContext("2d");
    g.clearRect(0, 0, c.width, c.height);
    g.strokeStyle = "#1a2e1a"; g.lineWidth = 2; g.lineCap = "round"; g.lineJoin = "round";
    for (const s of strokes) {
      if (!s.length) continue;
      g.beginPath();
      g.moveTo(s[0].x * c.width, s[0].y * c.height);
      for (let i = 1; i < s.length; i++) g.lineTo(s[i].x * c.width, s[i].y * c.height);
      if (s.length === 1) g.lineTo(s[0].x * c.width + 0.01, s[0].y * c.height);
      g.stroke();
    }
    // The ruled line, so what is drawn sits where it will print.
    g.strokeStyle = "#c8d8c8"; g.lineWidth = 1;
    g.beginPath(); g.moveTo(8, c.height - 10); g.lineTo(c.width - 8, c.height - 10); g.stroke();
  }, [strokes]);

  const points = strokes.reduce((n, s) => n + s.length, 0);

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      if (!name.trim()) throw new Error("enter the name you sign as");
      if (!points) throw new Error("draw a signature first");
      const r = await adminApiFetch("/api/market?fn=fsa426&op=signature", {
        method: "POST",
        body: JSON.stringify({ strokes, aspect: W / H, signerName: name.trim() }),
      });
      const j = await apiJson(r, "the signature service");
      setMsg(`Saved — ${j.points} points, ${j.strokes} stroke${j.strokes === 1 ? "" : "s"}.`);
      setStrokes([]);
      refresh();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm("Remove the saved signature? Forms will build unsigned until you draw a new one.")) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await adminApiFetch("/api/market?fn=fsa426&op=signature", { method: "DELETE" });
      setMsg("Removed."); refresh();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const card = { background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: isMobile ? 12 : 16, marginBottom: 14 };
  const btn = (primary) => ({ padding: "7px 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 700,
    cursor: busy ? "default" : "pointer", border: primary ? "none" : "1px solid #c8d8c8",
    background: primary ? "#2d4a2d" : "#f2f6f2", color: primary ? "#fff" : "#1a2e1a", opacity: busy ? 0.6 : 1 });

  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", marginBottom: 4 }}>
        Your signature for item 11A
      </div>
      <div style={{ fontSize: 12, color: "#5a6a5a", marginBottom: 10, lineHeight: 1.5 }}>
        Draw it once and every 426 you generate is signed. Stored as pen strokes, not a
        picture, so it prints onto FSA's own form without altering their page.
        {onFile && onFile.tracking === false && (
          <div style={{ color: "#8a6d1f", fontWeight: 600, marginTop: 4 }}>
            ⚠ Not saved yet — <code>sql/081_fsa_426_signature.sql</code> has not been run.
          </div>
        )}
        {onFile && onFile.onFile && onFile.signature && (
          <div style={{ color: "#2d6a2d", fontWeight: 600, marginTop: 4 }}>
            ✓ On file for {onFile.signature.signer_name} — {onFile.signature.point_count || "?"} points,
            saved {String(onFile.signature.captured_at || "").slice(0, 10)}. Drawing a new one replaces it.
          </div>
        )}
        {onFile && onFile.onFile === false && onFile.tracking !== false && (
          <div style={{ color: "#8a6d1f", fontWeight: 600, marginTop: 4 }}>
            No signature on file — forms build unsigned until you save one.
          </div>
        )}
      </div>

      <canvas
        ref={canvasRef} width={W} height={H}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        style={{ width: "100%", maxWidth: W, height: "auto", aspectRatio: `${W} / ${H}`,
                 border: "1px solid #c8d8c8", borderRadius: 8, background: "#fcfdfc",
                 touchAction: "none", cursor: "crosshair", display: "block" }}
      />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name you sign as"
          style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid #c8d8c8", fontSize: 12.5, minWidth: 180 }} />
        <button style={btn(true)} disabled={busy || !points} onClick={save}>✓ Save signature</button>
        <button style={btn(false)} disabled={busy || !points} onClick={() => setStrokes([])}>Clear</button>
        {onFile && onFile.onFile && (
          <button style={btn(false)} disabled={busy} onClick={remove}>🗑 Remove saved</button>
        )}
        <span style={{ fontSize: 11, color: "#60735f" }}>{points ? `${points} points` : "draw above"}</span>
      </div>

      {msg && <div style={{ fontSize: 12, color: "#2d6a2d", fontWeight: 600, marginTop: 8 }}>{msg}</div>}
      {err && <div style={{ fontSize: 12, color: "#a33", fontWeight: 600, marginTop: 8 }}>{err}</div>}
      <div style={{ fontSize: 11, color: "#60735f", marginTop: 8, lineHeight: 1.5 }}>
        Only the PDF is signed. The .docx stays unsigned on purpose — it is the editable
        copy, and adding an image to a Word file would mean rewriting its zip rather than
        stamping a field.
      </div>
    </div>
  );
}

function Fsa426Panel() {
  // Kevin, 2026-09-24: *"This feature needs to be mobile friendly … The layout right now
  // is also a little weird on mobile."* This panel was built desktop-first and had no
  // width awareness at all.
  const isMobile = useIsMobile();
  const nowY = new Date().getFullYear();
  const [cropYear, setCropYear] = useState(nowY + 1);
  const [crop, setCrop] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState({});        // "ST|COUNTY" -> Set(farmerId) | "all"
  const [wantMaps, setWantMaps] = useState({});    // "ST|COUNTY" -> bool
  const [farmerQ, setFarmerQ] = useState("");     // narrows what is SHOWN, never what is picked
  const [countyQ, setCountyQ] = useState("");     // ditto, on the county the form is addressed to
  const [busy, setBusy] = useState(false);
  const [built, setBuilt] = useState(null);
  const [sent, setSent] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null); setBuilt(null);
    try {
      const q = `/api/market?fn=fsa426&op=candidates&cropYear=${cropYear}` + (crop ? `&crop=${encodeURIComponent(crop)}` : "");
      const r = await adminApiFetch(q);
      const j = await apiJson(r, "the FSA-426 service");
      setData(j);
      // ⚠ NOTHING IS SELECTED BY DEFAULT (Kevin, 2026-09-23: "the default is that all counties
      // are checked so I would have to uncheck all counties if I only wanted to send one").
      // Selecting everything makes the COMMON case — one county, or a handful of growers — the
      // most work, and it makes an accidental Generate the widest possible action. "All" is one
      // click away for the batch case, which is the rarer one.
      setPicked({});
    } catch (e) { setErr(e.message); setData(null); }
    setLoading(false);
  };
  const loadSent = async () => {
    try {
      const r = await adminApiFetch(`/api/market?fn=fsa426&op=sent&programYear=${cropYear}`);
      const j = await apiJson(r, "the FSA-426 service");
      setSent(j);
    } catch { setSent(null); }
  };
  useEffect(() => { load(); loadSent(); /* eslint-disable-next-line */ }, []);

  const key = (c) => c.state + "|" + c.county;
  const chosenOf = (c) => {
    const p = picked[key(c)];
    if (p === "all") return c.producers;
    if (p instanceof Set) return c.producers.filter((x) => p.has(x.farmerId));
    return [];
  };
  const toggleFarmer = (c, fid) => setPicked((m) => {
    const k = key(c);
    const cur = m[k] === "all" ? new Set(c.producers.map((x) => x.farmerId)) : new Set(m[k] || []);
    if (cur.has(fid)) cur.delete(fid); else cur.add(fid);
    return { ...m, [k]: cur };
  });
  const setCounty = (c, on) => setPicked((m) => ({ ...m, [key(c)]: on ? "all" : new Set() }));

  // ── SELECT ALL / NONE, AT BOTH LEVELS (Kevin, 2026-09-23) ──────────────────
  // "I need the flexibility to select crop, farmer, county with select all or none etc.
  // right now I dont have the ability to send a 426 for only select farmers."
  // Counties with no FSA office on file are never selected by All — they cannot be
  // generated, and including them would make the count promise forms that will not build.
  // With a county filter on, "select all" means the ones on screen — that is what a person
  // means by it, and the button's own label says which it is doing. With no filter it is
  // every county, unchanged. Counties with no FSA office are never selected either way.
  const allCounties = (on) => setPicked(on
    ? Object.fromEntries((data ? data.counties : [])
        .filter((c) => c.office && (!countyQ.trim() || countyMatches(c)))
        .map((c) => [key(c), "all"]))
    : {});
  const setFarmersIn = (c, ids) => setPicked((m) => ({ ...m, [key(c)]: new Set(ids) }));

  // ⚠ THE FILTER NARROWS WHAT IS SHOWN, NEVER WHAT IS SELECTED. A name typed here hides
  // rows; it must not silently drop a grower already ticked, or a batch would quietly
  // shrink between typing and pressing Generate. `live` below reads `picked`, not the
  // filter, so what generates is what was chosen.
  const q = farmerQ.trim().toLowerCase();
  const matches = (p) => !q || String(p.name || "").toLowerCase().includes(q);
  const visibleProducers = (c) => c.producers.filter(matches);
  // The county filter reads the row's own label — "NY LIVINGSTON" — so "NY", "LIV" and
  // "NY LIV" all hit, rather than only the bare county name the data happens to store.
  const cq = countyQ.trim().toLowerCase();
  const countyMatches = (c) => !cq || (c.state + " " + c.county).toLowerCase().includes(cq);
  const countyVisible = (c) => countyMatches(c) && (!q || visibleProducers(c).length > 0);
  const shownCounties = (data ? data.counties : []).filter(countyVisible);

  const live = (data ? data.counties : []).filter((c) => c.office && chosenOf(c).length);
  const totalProducers = live.reduce((s, c) => s + chosenOf(c).length, 0);
  const totalForms = live.reduce((s, c) => s + Math.max(1, Math.ceil(chosenOf(c).length / (data ? data.perForm : 12))), 0);

  const generate = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        programYear: cropYear,
        counties: live.map((c) => ({
          state: c.state, county: c.county,
          producers: chosenOf(c).map((p) => ({
            name: p.name, idLast4: p.idLast4, idTypeCode: p.idTypeCode,
            want578: true, wantMaps: !!wantMaps[key(c)],
          })),
        })),
      };
      const r = await adminApiFetch("/api/market?fn=fsa426&op=build",
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const j = await apiJson(r, "the FSA-426 service");
      setBuilt(j);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const bytesOf = (b64) => {
    const b = atob(b64);
    const arr = new Uint8Array(b.length);
    for (let i = 0; i < b.length; i++) arr[i] = b.charCodeAt(i);
    return arr;
  };

  // ── THE FORM OPENS ON SCREEN (Kevin, 2026-09-24) ──────────────────────────
  //
  // Asked what should happen when he taps a form on his phone, he picked: *the 426
  // opens full-screen in the phone's PDF viewer, and I use its own share button to
  // save or email it.* So a 426 is a LINK TO A DOCUMENT, not a download.
  //
  // ⚠ IT IS AN <a>, NOT A CLICK HANDLER, AND THAT IS THE WHOLE POINT. A
  // `window.open()` fired from JavaScript is popup-blocked on iOS often enough to
  // be unreliable, and a `download` attribute is IGNORED there outright — which is
  // how two earlier attempts ended with a tap that appeared to do nothing. A plain
  // anchor with target=_blank is never blocked, and iOS renders the PDF in its own
  // viewer with share, print and done already on it. Nothing to reimplement.
  //
  // The URL is made once per build and revoked with it: making one per render leaks
  // a blob for every repaint, and revoking on click would break the back button.
  const pdfUrls = useMemo(() => {
    const m = {};
    if (!built) return m;
    const add = (k, b64) => {
      if (!b64) return;
      try { m[k] = URL.createObjectURL(new Blob([bytesOf(b64)], { type: "application/pdf" })); }
      catch { /* a blob we cannot build simply has no link; the button is not rendered */ }
    };
    if (built.combined && built.combined.pdfBase64) add("__all__", built.combined.pdfBase64);
    for (const d of built.documents || []) add(d.filename, d.pdfBase64);
    return m;
    // Keyed on `built` alone on purpose: `bytesOf` is re-created every render and
    // adding it would re-mint every blob URL on every repaint — which is the leak
    // this memo exists to prevent.
  }, [built]);
  useEffect(() => () => { for (const u of Object.values(pdfUrls)) URL.revokeObjectURL(u); }, [pdfUrls]);

  // The Word version is still produced and still offered, one file per page — a PDF is not
  // editable, and an office that asks for a change is easier to answer from the .docx.
  // It stays a DOWNLOAD: a phone cannot render a .docx, so opening one on screen would
  // show the blank preview Kevin already met once.
  const download = async (f) => {
    const mime = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    const blob = new Blob([bytesOf(f.docxBase64)], { type: mime });
    try {
      if (typeof File === "function" && navigator.canShare) {
        const file = new File([blob], f.filename, { type: mime });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: f.filename });
          return;
        }
      }
    } catch (e) {
      // A share the user DISMISSED is not a failure and must not then force a download —
      // that is two saves for one tap.
      if (e && e.name === "AbortError") return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = f.filename;
    if (!("download" in a)) a.target = "_blank";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // ⚠ RECORDING IS A SEPARATE, DELIBERATE ACT. A generated form is not a sent form — Kevin
  // signs and sends them by hand — so this button is what writes the log. A download that
  // recorded itself would report every discarded draft as a request FSA received, and then
  // "we already asked Livingston" is false exactly when it matters.
  const record = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await adminApiFetch("/api/market?fn=fsa426&op=record", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requests: live.map((c) => ({
          state: c.state, county: c.county, programYear: cropYear, crop: crop || null,
          producers: chosenOf(c).map((p) => ({ name: p.name })),
          producerCount: chosenOf(c).length,
          forms: Math.max(1, Math.ceil(chosenOf(c).length / (data ? data.perForm : 12))),
          requested578: true, requestedMaps: !!wantMaps[key(c)], delivery: "email",
        })) }),
      });
      await apiJson(r, "the FSA-426 service");
      await loadSent();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const card = { background: "#fff", border: "1px solid #e3ebe3", borderRadius: 10, padding: 14, marginBottom: 12 };
  const btn = (on) => ({ padding: "7px 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
    border: "1px solid " + (on ? "#2d4a2d" : "#cfdccf"), background: on ? "#2d4a2d" : "#fff", color: on ? "#fff" : "#2d4a2d" });
  const sentBy = {};
  for (const s of (sent && sent.requests) || []) {
    const k = String(s.state).toUpperCase() + "|" + String(s.county).toUpperCase();
    if (!sentBy[k]) sentBy[k] = s;
  }

  return (
    <div>
      {/* The signature comes FIRST because it is a once-ever setup step, and a reader who
          scrolls past it generates unsigned forms without knowing the option existed.
          It collapses after it is on file. */}
      <Fsa426RequesterBlock />
      <Fsa426SignatureCapture />

      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>
          🏛️ FSA-426 — MPCI/FCIC Information Request
        </div>
        <div style={{ fontSize: 12.5, color: "#5c6f5c", lineHeight: 1.5, marginBottom: 10 }}>
          Stamps FSA's own form to request 578 producer prints. One form per county office,
          {data ? ` ${data.perForm}` : " 12"} producers each — a grower who farms in three counties
          appears on three forms, because that is how the records are held.
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#2d4a2d" }}>Program year{" "}
            <input type="number" value={cropYear} onChange={(e) => setCropYear(+e.target.value)}
              style={{ width: 80, padding: "5px 7px", border: "1px solid #cfdccf", borderRadius: 6 }} /></label>
          {/* ⚠ A DROPDOWN, NOT A TYPE-IN (Kevin, 2026-09-24). A free-text crop could be
              misspelled or simply not grown, and both returned an empty screen that looked
              like a data gap rather than a crop nobody carries. The options are the crops
              this program year actually HOLDS, each with its grower count, built server-side
              from the UNFILTERED book — so picking one can never empty the picker itself.
              A crop list that has not loaded yet leaves the current value selectable rather
              than silently resetting it to all crops. */}
          <label style={{ fontSize: 12, fontWeight: 700, color: "#2d4a2d" }}>Crop{" "}
            <select value={crop} onChange={(e) => setCrop(e.target.value)}
              // A fixed 190 beside its own label, inside a padded card, is most of a phone's
              // width before anything else on the row is laid out. It caps rather than fixes.
              style={{ width: "100%", maxWidth: 190, padding: "5px 7px", border: "1px solid #cfdccf", borderRadius: 6, background: "#fff", boxSizing: "border-box" }}>
              <option value="">All crops</option>
              {(data && data.cropsAvailable ? data.cropsAvailable : (crop ? [{ crop, producers: null }] : []))
                .map((c) => (
                  <option key={c.crop} value={c.crop}>
                    {c.crop}{c.producers == null ? "" : ` (${c.producers})`}
                  </option>
                ))}
            </select></label>
          <button style={btn(true)} onClick={load} disabled={loading}>{loading ? "Loading…" : "Find producers"}</button>
        </div>
        {err && <div style={{ marginTop: 8, fontSize: 12.5, color: "#8a3324", fontWeight: 600 }}>⚠ {err}</div>}
      </div>

      {data && (
        <div style={card}>
          <div style={{ fontSize: 13, color: "#33402f", marginBottom: 8 }}>
            <b>{data.totals.producers}</b> producers across <b>{data.totals.counties}</b> counties
            {data.crop ? <> growing <b>{data.crop}</b></> : null} in <b>{data.cropYear}</b> ·
            selected: <b>{totalProducers}</b> producers, <b>{totalForms}</b> form{totalForms === 1 ? "" : "s"}
          </div>
          {/* ⚠ SELECT ALL / NONE, AND A NAME FILTER (Kevin, 2026-09-23). Nothing is selected
              when the list loads, so the one-county and few-growers cases are the cheap ones.
              The filter narrows what is SHOWN and never what is picked — a batch that quietly
              shrank between typing a name and pressing Generate would be the worst kind of
              wrong, because the form would still look right. */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "8px 0 10px" }}>
            <button style={btn(false)} onClick={() => allCounties(true)}>
              {countyQ ? `Select the ${shownCounties.filter((c) => c.office).length} shown` : "Select all counties"}
            </button>
            <button style={btn(false)} onClick={() => allCounties(false)}>Select none</button>
            {/* ⚠ A COUNTY FILTER (Kevin, 2026-09-24: "Need a filter by county"). The form is
                addressed to a county office, so the county is the unit this screen works in —
                at 42 counties the list is long enough that finding one means scrolling past
                forty. It matches the state too ("NY", "NY LIV", "LIVINGSTON"), because the
                rows are labelled "NY LIVINGSTON" and a filter that ignored the half you can
                see would read as broken.
                It narrows what is SHOWN and never what is picked — same rule as the farmer
                filter beside it. The one place it DOES act is "Select the N shown", which is
                what a person means by select-all while a filter is on, and the button says
                so rather than leaving it to be discovered. */}
            <input value={countyQ} onChange={(e) => setCountyQ(e.target.value)}
              placeholder="filter by county…"
              style={{ flex: "1 1 150px", minWidth: 130, padding: "6px 9px", borderRadius: 7,
                       border: "1px solid " + (countyQ ? "#2d6a2d" : "#cfdccf") }} />
            {countyQ && <button style={btn(false)} onClick={() => setCountyQ("")}>clear county</button>}
            <input value={farmerQ} onChange={(e) => setFarmerQ(e.target.value)}
              placeholder="filter by farmer name…"
              style={{ flex: "1 1 200px", minWidth: 160, padding: "6px 9px", borderRadius: 7,
                       border: "1px solid " + (farmerQ ? "#2d6a2d" : "#cfdccf") }} />
            {farmerQ && (
              <button style={btn(false)} onClick={() => setFarmerQ("")}>clear filter</button>
            )}
            {/* Ticking exactly the growers the filter is showing, across every county they
                farm — which is the "send a 426 for only these farmers" case. */}
            {farmerQ && (
              <button style={btn(true)} onClick={() => setPicked(Object.fromEntries(
                data.counties.filter((c) => c.office && visibleProducers(c).length)
                  .map((c) => [key(c), new Set(visibleProducers(c).map((p) => p.farmerId))])))}>
                Select only the {data.counties.filter((c) => c.office).reduce((s, c) => s + visibleProducers(c).length, 0)} matching
              </button>
            )}
          </div>
          {farmerQ && (
            <div style={{ fontSize: 11.5, color: "#6b7a6b", marginBottom: 6 }}>
              Showing growers matching “{farmerQ}”. Anything already ticked stays ticked, whether
              or not it is on screen — the count above is what will generate.
            </div>
          )}
          {/* ⚠ NAMED, NOT DROPPED. A county with no office on file cannot be generated, and a
              grower silently missing from a book-wide request only surfaces at a claim. */}
          {data.totals.blockedCounties > 0 && (
            <div style={{ fontSize: 12.5, color: "#8a3324", fontWeight: 600, marginBottom: 6 }}>
              ⚠ {data.totals.blockedCounties} county cannot be generated — no FSA office address on file:{" "}
              {data.counties.filter((c) => !c.office).map((c) => c.state + " " + c.county).join(", ")}.
              The {data.officesOnFile} offices on file were read off forms FSA has already accepted; send me the
              address for this one and it joins them.
            </div>
          )}
          {/* The migration is a separate deploy step, so its absence is stated rather than
              showing up as a book with no tax IDs in it. */}
          {data.idColumns === false ? (
            <div style={{ fontSize: 12.5, color: "#8a6d1f", fontWeight: 600, marginBottom: 6 }}>
              ⚠ Producer ID column 6 will print blank — <code>sql/072_farmer_tax_id_last4.sql</code> has not
              been run yet. Once it is, the next M13 sync fills the last four digits and the type from the AIP file.
            </div>
          ) : data.totals.missingId > 0 ? (
            <div style={{ fontSize: 12.5, color: "#8a6d1f", fontWeight: 600, marginBottom: 6 }}>
              ⚠ {data.totals.missingId} of {data.totals.producers} producers have no ID on file — those rows print
              column 6 blank.
            </div>
          ) : null}

          <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #eef3ee", borderRadius: 8 }}>
            {/* ⚠ AN EMPTY LIST NAMES THE FILTER THAT EMPTIED IT. A mistyped county otherwise
                reads as "this crop year has no counties" — the same rule the outstanding-
                premium pane already follows. */}
            {data.counties.filter((c) => countyVisible(c) || chosenOf(c).length).length === 0 && (
              <div style={{ padding: "14px 12px", fontSize: 12.5, color: "#6b7a6b" }}>
                Nothing matches{countyQ ? <> county <b>“{countyQ}”</b></> : null}
                {countyQ && farmerQ ? " and" : null}
                {farmerQ ? <> farmer <b>“{farmerQ}”</b></> : null}. The book has{" "}
                <b>{data.totals.counties}</b> counties for {data.cropYear}
                {data.crop ? <> growing <b>{data.crop}</b></> : null}.
              </div>
            )}
            {data.counties.filter((c) => countyVisible(c) || chosenOf(c).length).map((c) => {
              const k = key(c), chosen = chosenOf(c), all = picked[k] === "all";
              const last = sentBy[k];
              return (
                <div key={k} style={{ padding: "8px 10px", borderBottom: "1px solid #f2f6f2",
                  opacity: c.office ? 1 : 0.55 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <input type="checkbox" checked={all || chosen.length > 0} disabled={!c.office}
                      onChange={(e) => setCounty(c, e.target.checked)} />
                    <b style={{ fontSize: 13.5 }}>{c.state} {c.county}</b>
                    <span style={{ fontSize: 12, color: "#6b7a6b" }}>
                      {chosen.length} of {c.producers.length} producer{c.producers.length === 1 ? "" : "s"}
                      {c.office ? ` · ${Math.max(1, Math.ceil(chosen.length / data.perForm))} form${chosen.length > data.perForm ? "s" : ""}` : ""}
                    </span>
                    {c.office && (
                      <label style={{ fontSize: 11.5, color: "#2d4a2d", fontWeight: 600 }}>
                        <input type="checkbox" checked={!!wantMaps[k]}
                          onChange={(e) => setWantMaps((m) => ({ ...m, [k]: e.target.checked }))} /> also map photocopies (8B)
                      </label>
                    )}
                    {last && <span style={{ fontSize: 11, color: "#6b7a6b" }}>
                      · last sent {String(last.sent_at).slice(0, 10)}</span>}
                  </div>
                  {!c.office && <div style={{ fontSize: 11.5, color: "#8a3324", marginLeft: 24 }}>{c.blocked}</div>}
                  {/* ⚠ OPEN BY DEFAULT ONCE THE COUNTY IS IN PLAY, and open whenever a name
                      filter is active. Kevin: "right now I dont have the ability to send a 426
                      for only select farmers" — the ability existed, buried inside a collapsed
                      details element, which for the reader is the same as not existing. */}
                  {c.office && (
                    <details style={{ marginLeft: 24, marginTop: 3 }} open={!!q || chosen.length > 0}>
                      <summary style={{ fontSize: 11.5, color: "#6b7a6b", cursor: "pointer" }}>
                        {c.producers.length} producer{c.producers.length === 1 ? "" : "s"}
                        {q ? ` · ${visibleProducers(c).length} matching` : ""}
                      </summary>
                      <div style={{ display: "flex", gap: 8, margin: "4px 0 2px" }}>
                        <button style={{ ...btn(false), padding: "3px 8px", fontSize: 11 }}
                          onClick={() => setFarmersIn(c, visibleProducers(c).map((p) => p.farmerId))}>
                          all{q ? " matching" : ""}
                        </button>
                        <button style={{ ...btn(false), padding: "3px 8px", fontSize: 11 }}
                          onClick={() => setFarmersIn(c, [])}>none</button>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 14px", marginTop: 4 }}>
                        {visibleProducers(c).map((p) => (
                          // ⚠ A minWidth WIDER THAN A PHONE IS A HORIZONTAL SCROLLBAR ON THE
                          // WHOLE PAGE. This was a flat `minWidth: 250`, which on a 390pt
                          // screen — inside a card with its own padding, nested two
                          // containers deep — pushed the File Management page sideways and
                          // cut the headings off at the left, which is the screen Kevin
                          // sent. The two columns it buys are a desktop convenience; on a
                          // phone the list is one column and the row simply wraps.
                          <label key={p.farmerId} style={{ fontSize: 11.5, color: "#33402f",
                                   minWidth: isMobile ? 0 : 250, flex: isMobile ? "1 1 100%" : "0 1 auto",
                                   display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.35 }}>
                            <input type="checkbox" style={{ marginTop: 2, flex: "0 0 auto" }}
                              checked={all || (picked[k] instanceof Set && picked[k].has(p.farmerId))}
                              onChange={() => toggleFarmer(c, p.farmerId)} />
                            {/* One flex item, not three: the name and its ID have to wrap as
                                one block, or a long farm name pushes the ID onto its own
                                line away from the tick it belongs to. */}
                            <span style={{ flex: "1 1 auto", minWidth: 0 }}>
                              {p.name}{" "}
                              <span style={{ color: p.idLast4 ? "#6b7a6b" : "#8a6d1f", whiteSpace: "nowrap" }}>
                                {p.idLast4 ? `··· ${p.idLast4} ${p.idType || ""}` : "no ID on file"}
                              </span>
                            </span>
                          </label>
                        ))}
                        {/* A grower ticked but hidden by the filter is still going to generate,
                            so the row says so rather than letting the list look shorter than the
                            count above it. */}
                        {q && chosen.some((p) => !matches(p)) && (
                          <div style={{ fontSize: 11, color: "#8a6d1f", fontWeight: 600, width: "100%" }}>
                            + {chosen.filter((p) => !matches(p)).length} more selected here but hidden by the filter
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button style={btn(true)} onClick={generate} disabled={busy || !live.length}>
              {busy ? "Working…" : `Generate ${totalForms} form${totalForms === 1 ? "" : "s"}`}
            </button>
            <button style={btn(false)} onClick={record} disabled={busy || !live.length}>
              Mark as sent to FSA
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#60735f", marginTop: 6 }}>
            Generating does not record anything — "Mark as sent" is what writes the log, because a form
            is only a request once you have actually sent it.
          </div>
        </div>
      )}

      {built && (
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 8 }}>
            {built.forms.length} form{built.forms.length === 1 ? "" : "s"} ready
          </div>
          {built.refused.length > 0 && (
            <div style={{ fontSize: 12.5, color: "#8a3324", marginBottom: 6 }}>
              Refused: {built.refused.map((r) => `${r.state} ${r.county} — ${r.reason}`).join("; ")}
            </div>
          )}
          {built.forms.some((f) => f.over.length) && (
            <div style={{ fontSize: 12.5, color: "#8a6d1f", marginBottom: 6 }}>
              ⚠ A name is longer than the official 60-character cell and prints shortened:{" "}
              {built.forms.flatMap((f) => f.over.map((o) => o.value)).join("; ")}
            </div>
          )}
          {/* ⚠ A COUNTY WHOSE PDF COULD NOT BE DRAWN SAYS SO. The Word file is still
              offered below, so nothing is lost — but a request must never look complete
              while one county quietly has no document. */}
          {/* ⚠ ONLY WHAT MAKES A FORM UNUSABLE. This used to fire on a name merely WIDER
              than its ruled box, and the first real request tripped it — Livingston's
              "WOODLAWN FARMS INC BY JOHN MORGAN" runs 31pt past its box and still stops
              12.4pt short of the tax-ID column. A perfectly good form, reported as one
              that "could not be drawn in full". A warning that fires on a good form is
              worse than none. */}
          {(built.documents || []).some((d) => d.issue) && (
            <div style={{ fontSize: 12.5, color: "#8a3324", marginBottom: 6 }}>
              ⚠ Check the PDF for{" "}
              {built.documents.filter((d) => d.issue).map((d) => `${d.state} ${d.county}`).join(", ")} — something
              did not land where it should. The Word version is correct.
            </div>
          )}
          {/* A real difference between the two formats, and not a fault: Word wraps a long
              name onto a second line and a fixed-coordinate overlay cannot. Said quietly,
              where it can be looked up, rather than as an alarm. */}
          {(built.documents || []).some((d) => d.wide) && (
            <div style={{ fontSize: 11.5, color: "#60735f", marginBottom: 6 }}>
              {built.documents.flatMap((d) => d.wide || []).map((w) => w.value).join("; ")}
              {" "}runs wider than its box on the PDF — still readable, and Word wraps it instead.
            </div>
          )}
          {/* ── OPEN, NOT DOWNLOAD (Kevin, 2026-09-24) ────────────────────────────
              Asked what a tap should do on his phone, he chose: the form opens on
              screen in the phone's own PDF viewer, and he saves or emails it from
              there. So these are LINKS, not buttons with click handlers — see
              `pdfUrls` above for why the distinction is load-bearing on iOS.
              Each county's file already carries EVERY page that county generated,
              which is the other half of *"if there are multiple 426s like with
              Livingston, open all of them"* — Livingston's three forms are three
              pages of one document, opened and saved once. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {built.combined && pdfUrls.__all__ && (
              <a href={pdfUrls.__all__} target="_blank" rel="noopener noreferrer"
                 style={{ ...btn(true), textDecoration: "none", textAlign: "center",
                          ...(isMobile ? { flex: "1 1 100%", padding: "12px 14px", fontSize: 14 }
                                       : { padding: "9px 14px" }) }}>
                📄 Open all {built.combined.pages} forms · {built.combined.counties.length} counties
              </a>
            )}
            {(built.documents || []).filter((d) => d.pdfBase64 && pdfUrls[d.filename]).map((d) => (
              <a key={d.filename} href={pdfUrls[d.filename]} target="_blank" rel="noopener noreferrer"
                 style={{ ...btn(!built.combined), textDecoration: "none", textAlign: "center",
                          ...(isMobile ? { flex: "1 1 100%", padding: "11px 14px", fontSize: 14 } : null) }}>
                {/* ⚠ THE STATE IS PART OF THE NAME. Found on production 2026-09-24 by
                    generating a real two-county request: a filter on "STEUBEN" matches
                    IN STEUBEN and NY STEUBEN, and both links rendered "Open STEUBEN" —
                    two different FSA offices in two different states, indistinguishable
                    on screen. County names repeat across the states this book writes in
                    (ERIE, FRANKLIN, MONROE, WAYNE, TIOGA and more), so this is the
                    ordinary case rather than an oddity, and sending the wrong file to
                    the wrong office is the cost. Same rule the wheat card already
                    carries: do not name a county without the state it is in. */}
                📄 Open {d.state} {d.county}{d.pages > 1 ? ` — all ${d.pages} forms` : ""}
              </a>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#60735f", marginTop: 6 }}>
            Opens in your PDF viewer — save, print or email it from there.
            {built.combined ? " Send each county its OWN file; the combined one is for reading and keeping." : ""}
          </div>
          {/* A combined file that could not be drawn is NAMED. The per-county files
              above are all still there, so nothing is lost — but a button that simply
              is not there reads as a screen that half-loaded. */}
          {built.combined && built.combined.error && (
            <div style={{ fontSize: 11.5, color: "#8a6d1f", marginTop: 4 }}>
              The single combined file could not be drawn ({built.combined.error}) — each county's
              own PDF above is unaffected.
            </div>
          )}
          {/* ── A .docx IS NOT A DOCUMENT ON A PHONE (Kevin, 2026-09-24) ──────────
              He tapped a Word button and got a blank iOS preview showing nothing but
              the filename — iOS renders a .docx as plain TEXT unless Pages is there to
              open it. The file itself was fine: fetched from production and opened in
              Word, 1 page / 4,042 characters / the real FSA-426 header.
              ⚠ AND HE TAPPED IT BECAUSE THIS SCREEN TOLD HIM TO. The over-wide-name
              banner above used to read "use the Word version for those" and fired on a
              form that was perfectly good. That banner is fixed; this is the other half
              — on a phone the Word files are folded away behind a disclosure and say
              what they need, rather than sitting as three equal-looking buttons under
              the one that actually works there. */}
          <details open={!isMobile} style={{ marginTop: 10 }}>
            <summary style={{ fontSize: 11.5, color: "#60735f", cursor: "pointer" }}>
              Word version, one file per page{isMobile ? " — needs Pages or a computer" : ""}
            </summary>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {built.forms.map((f) => (
                <button key={f.filename} style={btn(false)} onClick={() => download(f)}>
                  ⬇ {f.county}{f.of > 1 ? ` p${f.page}/${f.of}` : ""} ({f.producers})
                </button>
              ))}
            </div>
          </details>
          {/* ⚠ THE SIGNING LINE NOW REPORTS WHAT ACTUALLY HAPPENED, rather than instructing.
              It used to read "Sign item 11A by hand before sending" unconditionally, which
              was correct while nothing could sign — and would be a lie the moment something
              could. `built.signed` comes back from the build itself, so the sentence can only
              say "signed" when ink was drawn, and `signatureNote` carries the reason when it
              was not (no signature on file for this login, or the migration is unrun). */}
          <div style={{ fontSize: 11, color: built.signed ? "#2d6a2d" : "#8a6d1f", marginTop: 8, fontWeight: 600 }}>
            {built.signed
              ? `✓ Signed at item 11A${built.signedBy ? ` as ${built.signedBy}` : ""} — check it before sending.`
              : `Item 11A is NOT signed — sign by hand before sending.${built.signatureNote ? ` (${built.signatureNote})` : ""}`}
          </div>
          <div style={{ fontSize: 11, color: "#60735f", marginTop: 4 }}>
            Item 12 is left blank — it is FSA's to complete.
            {built.signed && " The .docx copy is unsigned: only the PDF carries the mark."}
          </div>
        </div>
      )}

      {sent && sent.tracking === false && (
        <div style={{ ...card, color: "#8a6d1f", fontSize: 12.5, fontWeight: 600 }}>
          ⚠ Request history is not being recorded — <code>sql/073_fsa_426_requests.sql</code> has not been run yet.
        </div>
      )}
      {sent && sent.requests && sent.requests.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a", marginBottom: 6 }}>
            Sent this program year
          </div>
          {sent.requests.slice(0, 20).map((s) => (
            <div key={s.id} style={{ fontSize: 12, color: "#33402f", padding: "3px 0", borderBottom: "1px solid #f2f6f2" }}>
              {String(s.sent_at).slice(0, 10)} · <b>{s.state} {s.county}</b> · {s.producer_count} producers ·
              {s.forms} form{s.forms === 1 ? "" : "s"}{s.crop ? ` · ${s.crop}` : ""}
              {s.requested_maps ? " · + maps" : ""}{s.sent_by ? ` · ${s.sent_by}` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Box connection panel — lives inside FSA578Screen Box tab.
// ─────────────────────────────────────────────────────────────────────────────
function BoxConnectionPanel({ onSyncComplete }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  // Tracks "Sync now" in-flight separately from per-account status.lastSyncStatus.
  // The Vercel function can take 60–300s; we need an immediate UI signal that
  // the click registered (otherwise users click again, stacking calls). The
  // per-account status doesn't update until loadStatus() re-fetches at the end.
  const [syncBusy, setSyncBusy] = useState(false);

  async function loadStatus() {
    setError(null);
    try {
      const r = await fetch("/api/box?action=status", { headers: getApiAuthHeaders() });
      const data = await apiJson(r, "Box");
      setStatus(data);
    } catch (e) {
      setError(e.message);
      setStatus({ connected: false });
    }
  }

  useEffect(() => {
    loadStatus();
    const onMsg = (ev) => {
      if (ev.data === "box-connected" || ev.data === "box-error") {
        setConnecting(false);
        loadStatus();
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // ⚠ The admin's session token goes in a HEADER, never in the address (it used
  // to be ?auth=<jwt>, which landed in Vercel logs and browser history; blueprint
  // B19). The popup opens blank INSIDE the click so a blocker allows it, then is
  // pointed at the authorize address the server hands back.
  async function connect() {
    setConnecting(true);
    const auth = getApiAuthHeaders().Authorization;
    if (!auth) { setError("Not signed in"); setConnecting(false); return; }
    const popup = window.open("", "box_oauth", "width=620,height=740");
    if (!popup) { setError("Popup blocked — allow popups for this site"); setConnecting(false); return; }
    try {
      const r = await adminApiFetch("/api/box?action=oauth-start&format=json", { credentials: "same-origin" });
      const d = await r.json().catch(() => null);
      if (!r.ok || !d || typeof d.url !== "string" || d.url.indexOf("https://account.box.com/") !== 0) {
        throw new Error((d && d.error) || `Box connect failed (HTTP ${r.status})`);
      }
      popup.location.href = d.url;
    } catch (e) {
      try { popup.close(); } catch (e2) { /* already closed */ }
      setError(String((e && e.message) || e)); setConnecting(false);
    }
  }

  if (status === null) {
    return <div style={{ padding: 30, textAlign: "center", color: "#60735f", fontSize: 14 }}>Loading Box status…</div>;
  }

  const fmtDate = (s) => s ? new Date(s).toLocaleString() : "—";
  // status.accounts is the array form returned by the multi-account API; older
  // singleton responses don't include it, so synthesize from the flat fields.
  const accounts = Array.isArray(status.accounts) && status.accounts.length
    ? status.accounts
    : (status.connected
        ? [{
            id: status.id || null,
            boxUserLogin: status.boxUserLogin,
            connectedAt: status.connectedAt,
            lastSyncAt: status.lastSyncAt,
            lastSyncStatus: status.lastSyncStatus,
            lastSyncError: status.lastSyncError,
            lastSync: status.lastSync,
          }]
        : []);
  const anyRunning = syncBusy || accounts.some(a => a.lastSyncStatus === "running");

  async function disconnectAccount(account) {
    if (!account?.id) return;
    if (!window.confirm(`Disconnect ${account.boxUserLogin || "this Box account"}?\n\nThis removes the OAuth tokens; historical 578s already imported from this account stay intact. You can re-authorize anytime.`)) return;
    setError(null);
    try {
      const r = await fetch(`/api/box?action=disconnect&id=${encodeURIComponent(account.id)}`, { method: "POST", headers: getApiAuthHeaders() });
      const data = await apiJson(r, "Box");
      await loadStatus();
    } catch (e) {
      setError(`Disconnect failed: ${e.message}`);
    }
  }

  return (
    <div style={{ padding: 4 }}>
      <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 30 }}>📦</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#1a2e1a" }}>Box integration</div>
            <div style={{ fontSize: 12, color: "#60735f" }}>Pull FSA-578s automatically from shared Box folders</div>
          </div>
          <span style={{
            fontSize: 11, fontWeight: 800, padding: "4px 10px", borderRadius: 12, letterSpacing: "0.06em",
            background: accounts.length ? "#e8f4e2" : "#fdf0e0",
            color: accounts.length ? "#2d6a1a" : "#8a3a00",
          }}>{accounts.length ? `● ${accounts.length} CONNECTED` : "○ NOT CONNECTED"}</span>
        </div>

        {error && (
          <div style={{ background: "#fdf0e0", border: "1px solid #e0a0a0", borderRadius: 7, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#8a3a00" }}>
            {error}
          </div>
        )}

        {accounts.length === 0 && (
          <div>
            <p style={{ fontSize: 13, color: "#4a6e4a", margin: "0 0 16px", lineHeight: 1.5 }}>
              Connect a Box account so the dashboard can scan shared folders for new FSA-578 PDFs and import them automatically. You can connect multiple Box accounts (e.g. <code>kkeenan@cloverag.com</code> + <code>office@cloverag.com</code>) — every Sync runs all of them.
            </p>
            <button onClick={connect} disabled={connecting}
              style={{ padding: "11px 22px", border: "none", borderRadius: 7, background: "#2d4a2d", color: "#fff", fontWeight: 800, fontSize: 13, cursor: connecting ? "default" : "pointer", letterSpacing: "0.06em", opacity: connecting ? 0.7 : 1 }}>
              {connecting ? "Opening Box…" : "🔗 CONNECT BOX"}
            </button>
            <div style={{ marginTop: 20, fontSize: 11, color: "#60735f", lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: "#4a6e4a", marginBottom: 6 }}>Admin setup required first:</div>
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                <li>Register a Custom App (User Authentication / OAuth 2.0) at developer.box.com</li>
                <li>Add redirect URI: <code style={{ background: "#f4f8f0", padding: "1px 5px", borderRadius: 3 }}>https://&lt;your-vercel-domain&gt;/api/box?action=oauth-callback</code></li>
                <li>Set Vercel env vars <code style={{ background: "#f4f8f0", padding: "1px 5px", borderRadius: 3 }}>BOX_CLIENT_ID</code> and <code style={{ background: "#f4f8f0", padding: "1px 5px", borderRadius: 3 }}>BOX_CLIENT_SECRET</code></li>
              </ol>
            </div>
          </div>
        )}

        {accounts.length > 0 && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 16 }}>
              {accounts.map((a) => (
                <div key={a.id || a.boxUserLogin} style={{ background: "#f4f8f0", border: "1px solid #dde8d8", borderRadius: 7, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#60735f", letterSpacing: "0.08em", textTransform: "uppercase" }}>Box account</div>
                    {a.id && (
                      <button onClick={() => disconnectAccount(a)} title="Remove this Box account"
                        style={{ background: "none", border: "none", color: "#c0392b", fontSize: 10, fontWeight: 700, cursor: "pointer", padding: 0, letterSpacing: "0.04em" }}>
                        Disconnect
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", marginTop: 2, wordBreak: "break-all" }}>{a.boxUserLogin || "—"}</div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 11, color: "#60735f" }}>
                    <span><b style={{ color: "#4a6e4a" }}>Connected:</b> {fmtDate(a.connectedAt)}</span>
                    <span style={{ color: a.lastSyncStatus === "error" ? "#c0392b" : undefined }}>
                      <b style={{ color: "#4a6e4a" }}>Last sync:</b> {a.lastSyncAt ? fmtDate(a.lastSyncAt) : "Never run"}
                      {a.lastSync?.scanned != null && ` · ${a.lastSync.scanned} scanned, ${a.lastSync.new || 0} new`}
                    </span>
                  </div>
                  {a.lastSyncError && (
                    <div style={{ fontSize: 10, color: "#c0392b", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{a.lastSyncError}</div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button onClick={async () => {
                setError(null);
                setDryRunResult(null);
                setDryRunBusy(true);
                try {
                  const r = await fetch("/api/box?action=sync&dryRun=true", { method: "POST", headers: getApiAuthHeaders() });
                  // Read body as text first so we can surface non-JSON
                  // Vercel platform errors (FUNCTION_INVOCATION_FAILED) and
                  // object-shaped error envelopes verbatim.
                  const raw = await r.text();
                  let data;
                  try { data = JSON.parse(raw); } catch { data = { error: raw.slice(0, 400) }; }
                  if (!r.ok) {
                    const errStr = typeof data.error === "string"
                      ? data.error
                      : (data.error?.message || data.errorMessage || JSON.stringify(data.error || data).slice(0, 400));
                    throw new Error(`HTTP ${r.status} — ${errStr}`);
                  }
                  setDryRunResult(data);
                } catch (e) {
                  setError(`Preview failed: ${e.message}`);
                } finally {
                  setDryRunBusy(false);
                }
              }}
                disabled={dryRunBusy || anyRunning}
                style={{ padding: "9px 18px", border: "1px solid #4a6e4a", borderRadius: 7, background: dryRunBusy ? "#dde8d8" : "#fff", color: "#2d4a2d", fontWeight: 700, fontSize: 12, cursor: dryRunBusy ? "wait" : "pointer" }}>
                {dryRunBusy ? "🔍 Previewing…" : "🔍 Preview sync (dry run)"}
              </button>
              <button onClick={async () => {
                if (!window.confirm("This will email Mandy every new 578 found in Box. Did you run Preview first to verify the list? Continue?")) return;
                setError(null);
                setDryRunResult(null);
                setSyncBusy(true);
                try {
                  const r = await fetch("/api/box?action=sync", { method: "POST", headers: getApiAuthHeaders() });
                  // Vercel platform errors arrive as JSON with an object error;
                  // mirror the dry-run defensive parse so the user sees the real
                  // message instead of "[object Object]".
                  const raw = await r.text();
                  let data;
                  try { data = JSON.parse(raw); } catch { data = { error: raw.slice(0, 400) }; }
                  if (!r.ok) {
                    const errStr = typeof data.error === "string"
                      ? data.error
                      : (data.error?.message || data.errorMessage || JSON.stringify(data.error || data).slice(0, 400));
                    throw new Error(`HTTP ${r.status} — ${errStr}`);
                  }
                  await loadStatus();
                  // Tell the parent FSA578Screen to refresh its records list
                  // so new Box-imported 578s appear in Tracking / By Farmer /
                  // header counts without a page reload.
                  if (typeof onSyncComplete === "function") {
                    try { await onSyncComplete(); } catch {}
                  }
                  if (data.errors?.length) {
                    setError(`Sync finished with ${data.errors.length} error(s). Check Tracking tab.`);
                  }
                } catch (e) {
                  setError(`Sync failed: ${e.message}`);
                  await loadStatus();
                } finally {
                  setSyncBusy(false);
                }
              }}
                disabled={anyRunning}
                style={{ padding: "9px 18px", border: "none", borderRadius: 7, background: anyRunning ? "#60735f" : "#2d4a2d", color: "#fff", fontWeight: 700, fontSize: 12, cursor: anyRunning ? "wait" : "pointer" }}>
                {anyRunning ? "🔄 Syncing…" : "🔄 Sync now (sends emails)"}
              </button>
              <button onClick={connect} disabled={connecting} title="Add another Box login (e.g. office@cloverag.com). Log out of Box in your browser first, or use an incognito window, to authorize a different account."
                style={{ padding: "9px 18px", border: "1px solid #c8d8c0", borderRadius: 7, background: "#fff", color: "#4a6e4a", fontWeight: 700, fontSize: 12, cursor: connecting ? "default" : "pointer", opacity: connecting ? 0.6 : 1 }}>
                {connecting ? "Opening Box…" : "+ Connect another account"}
              </button>
              <button onClick={loadStatus}
                style={{ padding: "9px 18px", border: "1px solid #c8d8c0", borderRadius: 7, background: "#fff", color: "#4a6e4a", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                ↺ Refresh
              </button>
            </div>

            {dryRunResult && (
              <div style={{ marginTop: 20, background: "#f4f8f0", border: "1px solid #c8d8c0", borderRadius: 8, padding: 16 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2e1a" }}>🔍 Dry-run preview (no emails sent, no rows written)</div>
                    <div style={{ fontSize: 11, color: "#60735f", marginTop: 2 }}>
                      Target year: <b>{dryRunResult.target_year}</b> · Scanned {dryRunResult.scanned} · Would import {dryRunResult.new} ({dryRunResult.matched} matched, {dryRunResult.unmatched} unmatched)
                    </div>
                  </div>
                  <button onClick={() => setDryRunResult(null)} style={{ padding: "4px 10px", border: "1px solid #c8d8c0", borderRadius: 5, background: "#fff", color: "#60735f", fontSize: 11, cursor: "pointer" }}>Dismiss</button>
                </div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10, fontSize: 11, color: "#4a6e4a" }}>
                  <span><b style={{ color: "#2d6a1a" }}>Would import:</b> {dryRunResult.new}</span>
                  <span title="Files already processed in a prior sync (imported as a 578 or recorded in box_scanned_files as wrong-year / not-578 / duplicate). Skipped before Anthropic — no cost."><b style={{ color: "#60735f" }}>Already scanned:</b> {dryRunResult.skipped_existing}</span>
                  <span><b style={{ color: "#8a6a00" }}>Wrong year:</b> {dryRunResult.skipped_wrong_year}</span>
                  <span><b style={{ color: "#8a6a00" }}>Year unknown:</b> {dryRunResult.skipped_year_unknown}</span>
                  <span><b style={{ color: "#60735f" }}>Pre-season (≤4/30):</b> {dryRunResult.skipped_stale || 0}</span>
                  <span><b style={{ color: "#60735f" }}>Duplicate:</b> {dryRunResult.skipped_duplicate}</span>
                  <span><b style={{ color: "#60735f" }}>Not a 578:</b> {dryRunResult.skipped_not_578}</span>
                  {dryRunResult.errors?.length > 0 && <span><b style={{ color: "#c0392b" }}>Errors:</b> {dryRunResult.errors.length}</span>}
                </div>
                {dryRunResult.previews?.length > 0 && (
                  <div style={{ maxHeight: 320, overflowY: "auto", background: "#fff", border: "1px solid #dde8d8", borderRadius: 6 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: "#eef6ec", textAlign: "left", position: "sticky", top: 0 }}>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid #c8d8c0" }}>File</th>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid #c8d8c0" }}>Producer</th>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid #c8d8c0" }}>County</th>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid #c8d8c0" }}>Year</th>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid #c8d8c0" }}>Decision</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dryRunResult.previews.map((p, i) => {
                          const isImport = p.decision === "would_import_matched" || p.decision === "would_import_unmatched";
                          const color = isImport
                            ? (p.decision === "would_import_matched" ? "#2d6a1a" : "#c0392b")
                            : "#60735f";
                          return (
                            <tr key={i} style={{ borderBottom: "1px solid #f0f4f0" }}>
                              <td style={{ padding: "5px 10px", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.file}>{p.file}</td>
                              <td style={{ padding: "5px 10px" }}>{p.extracted?.name || "—"}{p.match ? <span style={{ color: "#2d6a1a", fontWeight: 600 }}> → {p.match.name}</span> : ""}</td>
                              <td style={{ padding: "5px 10px" }}>{p.extracted?.county || "—"}{p.extracted?.is_nationwide ? " (NW)" : ""}</td>
                              <td style={{ padding: "5px 10px" }}>
                                {p.extracted?.year ?? "?"}
                                {p.box_created_at && (
                                  <div style={{ fontSize: 10, color: "#9aa09a", marginTop: 2 }} title={p.box_created_at}>
                                    Box: {new Date(p.box_created_at).toLocaleDateString()}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: "5px 10px", color, fontWeight: 700 }}>
                                {p.decision === "would_import_matched" && "✓ Would import (matched)"}
                                {p.decision === "would_import_unmatched" && "⚠ Would import (no match)"}
                                {p.decision === "skipped_wrong_year" && "✗ Wrong year"}
                                {p.decision === "skipped_year_unknown" && "✗ Year not on page 1"}
                                {p.decision === "skipped_stale" && "✗ Pre-season (uploaded before May 1)"}
                                {p.decision === "skipped_duplicate" && "↻ Duplicate"}
                                {p.decision === "skipped_not_578" && "✗ Not a 578"}
                                {p.is_revision && (
                                  <div style={{ fontSize: 10, fontWeight: 800, color: "#a06000", marginTop: 2 }}>🔄 REVISION (will bypass dedup)</div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {dryRunResult.new > 0 && (
                  <div style={{ marginTop: 12, padding: "8px 12px", background: "#fdf6e0", border: "1px solid #e0c8a0", borderRadius: 6, fontSize: 11, color: "#5a4a00" }}>
                    Running "Sync now" with these settings will email Mandy <b>{dryRunResult.new}</b> 578{dryRunResult.new === 1 ? "" : "s"}. Verify the list above before continuing.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DOCUMENT HUB PANEL  (per-farmer doc storage + Dropbox Sign e-sign)
// Lives as the "Documents" tab inside FSA578Screen. Stores Acreage Reports,
// Schedules of Insurance, etc. sorted by farmer; sends them for e-signature via
// Dropbox Sign (test_mode until the API plan is live). Filename → policy number →
// farmer auto-matching reuses the policies table as the source of truth.
// ─────────────────────────────────────────────────────────────────────────────
const DOC_TYPE_LABELS = {
  acreage_report: "Acreage Report",
  schedule_of_insurance: "Schedule of Insurance",
  "578": "FSA-578",
  other: "Other",
};
function parsePolicyCandidates(filename) {
  const s = (filename || "").toUpperCase();
  const out = new Set();
  // Order matters: try the most specific (full NAU) form first so the short
  // form regex doesn't capture a truncated piece of it.
  const patterns = [
    /\b[A-Z]{2}-\d{3}-\d{5,8}-\d{2}\b/g, // NAU full:  NY-944-6101410-26
    /\b[A-Z]{2}-\d{6,8}-\d{2}\b/g,        // NAU short: NY-0844634-26
    /\bMP-\d{6,8}\b/g,                     // R&H:       MP-0835689
  ];
  for (const re of patterns) { const m = s.match(re); if (m) m.forEach((x) => out.add(x)); }
  return [...out];
}
function inferDocType(name) {
  const s = (name || "").toLowerCase();
  if (/acreage/.test(s)) return "acreage_report";
  if (/schedule of insurance|\bsoi\b/.test(s)) return "schedule_of_insurance";
  if (/578|report of commodities/.test(s)) return "578";
  return "other";
}
// What the 578 intake (api/box.js ingestStored578) answered for a hub-uploaded 578.
const STORED_578_OUTCOME = {
  import: "filed in 578 Management and emailed to Mandy",
  // A filed 578 that was NOT emailed: a past campaign, or the send failed.
  import_unsent: "filed in 578 Management (not sent to Mandy — send it from the 578 board if it needs keying)",
  duplicate: "a 578 for this farm, year and county is already on file — not filed again",
  duplicate_content: "this exact file is already in 578 Management — not filed again",
  not_578: "the file name says 578 but the document is not an FSA-578 — kept as a document only",
  year_unknown: "the 578's program year could not be read — kept as a document only",
  extract_failed: "the 578 could not be read — kept as a document only",
  insert_failed: "the 578 could not be saved to 578 Management — kept as a document only",
};

// Remembers the last signed-copy destination folder so the picker re-opens there
// (you still confirm it every send — just fewer clicks to navigate back).
let _lastSignedDest = "";
// Dropbox Sign embedded library, loaded on demand from CDN (matches the d3Promise
// pattern). Exposes window.HelloSign; used by the in-app box-placement editor.
let _hsEmbeddedPromise = null;
function loadHelloSignEmbedded() {
  if (typeof window !== "undefined" && window.HelloSign) return Promise.resolve(window.HelloSign);
  if (_hsEmbeddedPromise) return _hsEmbeddedPromise;
  _hsEmbeddedPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.hellosign.com/public/js/embedded/v2.11.1/embedded.production.min.js";
    s.async = true;
    s.onload = () => (window.HelloSign ? resolve(window.HelloSign) : reject(new Error("Dropbox Sign embedded library failed to initialize")));
    s.onerror = () => reject(new Error("Failed to load the Dropbox Sign embedded library"));
    document.head.appendChild(s);
  });
  return _hsEmbeddedPromise;
}

// ─────────────────────────────────────────────────────────────────────────────
// Acreage Report Builder — shared Fill/Check control (used on both 578 rows and
// acreage-report doc rows). Two entry modes:
//   • 578-first:  pass { farmerId, report578Path } → lets you PICK which acreage
//                 report to fill, then reconciles it against THIS 578.
//   • form-first: pass { formDoc } → fills that specific acreage report.
// Flow: pick form (578-first) → preview (parse 578 + reconcile + acre-verify +
// discrepancies) → generate stamped PDF. Nothing is stamped until you click.
// ─────────────────────────────────────────────────────────────────────────────
// ── Shared Dropbox folder picker ─────────────────────────────────────────────
// Browse the connected account's folder tree and pick a destination. Remembers the
// last pick across opens (module-level, same as _lastSignedDest) so a repeat run
// doesn't re-navigate. DocumentHubPanel still has its own inline copy for the
// e-sign destination — worth folding into this once that flow can be exercised
// end-to-end; not worth risking a working signature path on a blind refactor.
let _lastFilledDest = "";
function DropboxFolderPicker({ initialPath = "", title = "Choose a Dropbox folder", confirmLabel = "Use this folder", busy = false, onPick, onClose }) {
  const [path, setPath] = useState(initialPath);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function browse(p) {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`/api/market?fn=dropbox&action=list-folders&path=${encodeURIComponent(p)}`, { headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setPath(d.path != null ? d.path : p);
      setFolders(d.folders || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }
  useEffect(() => { browse(initialPath); }, []); // mount only — the picker owns `path` from here

  const up = () => browse(path.split("/").slice(0, -1).join("/"));
  return (
    <div onClick={() => !busy && onClose()} style={{ position: "fixed", inset: 0, background: "rgba(20,30,20,.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, maxWidth: 520, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column", padding: 18, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: "#1a2e1a" }}>{title}</h3>
          {!busy && <button onClick={onClose} style={{ marginLeft: "auto", border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "#60735f" }}>✕</button>}
        </div>
        <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8, wordBreak: "break-all" }}>{path || "/ (account root)"}</div>
        {error && <div style={{ fontSize: 11, color: "#8a3a00", background: "#fdf0e0", border: "1px solid #f0d6b0", borderRadius: 7, padding: "7px 10px", marginBottom: 8 }}>{error}</div>}
        <div style={{ flex: 1, overflow: "auto", border: "1px solid #dde8d8", borderRadius: 8, marginBottom: 10, minHeight: 140 }}>
          {loading ? <div style={{ padding: 20, textAlign: "center", color: "#60735f", fontSize: 12 }}>Loading…</div> : (
            <>
              {path && <button onClick={up} style={{ display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid #edf2e8", background: "#fafcf8", cursor: "pointer", fontSize: 13, color: "#4a6e4a", padding: "8px 12px" }}>↩ up one level</button>}
              {folders.map(f => (
                <div key={f.path} style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #edf2e8" }}>
                  <button onClick={() => browse(f.path)} style={{ flex: 1, textAlign: "left", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: "#1a2e1a", padding: "8px 12px" }}>📁 {f.name}</button>
                </div>
              ))}
              {!folders.length && <div style={{ padding: 18, textAlign: "center", color: "#9bab97", fontSize: 12 }}>No subfolders here — you can still file into this folder.</div>}
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {!busy && <button onClick={onClose} style={acrModalBtn("1px solid #c8d8c0", "#fff", "#4a6e4a")}>Cancel</button>}
          <button onClick={() => onPick(path)} disabled={!path || busy}
            title={path ? "" : "Open a folder first — files can't go to the account root"}
            style={{ ...acrModalBtn("none", path && !busy ? "#1f6b1a" : "#c8d8c0", "#fff"), cursor: path && !busy ? "pointer" : "not-allowed" }}>
            {busy ? "Filing…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function acrModalBtn(border, bg, color) {
  return { fontSize: 13, fontWeight: 700, padding: "7px 14px", borderRadius: 8, border, background: bg, color, cursor: "pointer" };
}
// fillSeason: "spring" | "fall" — which campaign the caller is working, passed to the
// server so its 578 auto-pick prefers that season's document. Defaults to null rather
// than to "spring": a default here is the bug being fixed, one level up.
function AcreageFillButton({ formDoc = null, farmerId = null, report578Path = null, farmerName = "", county = null, cropYear = null, label = "🌾 Fill Acreage Report", btnStyle = null, onFilled = null, formState = null, fillSeason = null }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // loading | select | review | filling
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState("");
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);
  const [filledUrl, setFilledUrl] = useState(null);
  const [filledPath, setFilledPath] = useState(null);
  const [sent, setSent] = useState(null); // null | "sending" | "sent"
  const [typeChoices, setTypeChoices] = useState({}); // crop -> "grain" | "reported" (grain+silage keying)
  const [avail578, setAvail578] = useState(null);     // FSA-578s on file for this farmer (null until loaded)
  const [sel578, setSel578] = useState(new Set());    // storage_paths of the 578(s) the agent chose to reconcile against

  const defaultBtn = { fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 5, border: "1px solid #1f6b1a", background: "#eef7ea", color: "#1f6b1a", cursor: "pointer" };

  function close() { setOpen(false); setPhase("idle"); setError(null); setReview(null); setFilledUrl(null); setFilledPath(null); setSent(null); setForms([]); setFormId(""); setTypeChoices({}); setAvail578(null); setSel578(new Set()); }
  // toggle a 578 in/out of the selection; changing the set invalidates the last preview/fill
  function toggle578(path) {
    setSel578((prev) => { const n = new Set(prev); n.has(path) ? n.delete(path) : n.add(path); return n; });
    setFilledUrl(null); setFilledPath(null); setSent(null);
  }
  // change how a grain+silage crop is keyed; a new choice invalidates the last filled PDF
  function pickType(crop, val) { setTypeChoices((t) => ({ ...t, [crop]: val })); setFilledUrl(null); setFilledPath(null); setSent(null); }

  async function api(action, payload) {
    const r = await fetch(`/api/market?fn=acreage-fill&action=${action}`, {
      method: "POST", headers: { ...getApiAuthHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(payload),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(typeof d.error === "string" ? d.error : JSON.stringify(d).slice(0, 200));
    return d;
  }
  function reqBody(selFormId, selPathsOverride) {
    const b = {};
    if (selFormId) b.form_document_id = selFormId;
    else if (formDoc) b.form_document_id = formDoc.id;
    // Explicit 578 selection wins; else fall back to a caller-supplied path, else let
    // the server auto-pick (nationwide preferred). Selecting several merges them. The
    // override lets the caller pass a just-computed set before React state has settled.
    const chosen = selPathsOverride || sel578;
    if (chosen && chosen.size) b.report_578_storage_paths = [...chosen];
    else if (report578Path) b.report_578_storage_path = report578Path;
    if (farmerId) b.farmer_id = farmerId;
    if (Object.keys(typeChoices).length) b.typeChoices = typeChoices;
    // ⚠ WHICH CAMPAIGN THIS FILL IS FOR, so the server's 578 auto-pick prefers the
    // matching season. Hardcoded to spring, the picker hands a fall (wheat) fill the
    // grower's SPRING 578 — measured on McIlroy, the book's only fall 578. Absent, the
    // server falls back to the crop year through the shared campaign rule; sending it
    // is what makes the answer about the tab the agent is looking at.
    if (fillSeason) b.season = fillSeason;
    return b;
  }
  async function start() {
    setOpen(true); setError(null); setReview(null); setFilledUrl(null); setFilledPath(null); setSent(null);
    setPhase("loading");
    // ONLY the report staged in "SIGNED & READY / Ready for Silas" is ever filled
    // (Kevin's rule, 2026-07-21). The live search is the single source of the form —
    // a clicked document (the Library's "Fill / Check") is NOT a fallback, because
    // honouring it is exactly how Lismore, Fire Creek and Sullivan got stamped off a
    // main-folder pre-print while their staged copy sat untouched. If nothing is
    // staged, say so and stop; staging it is one drag in Dropbox.
    const fid = farmerId || (formDoc && formDoc.farmer_id) || null;
    const yr = cropYear || (formDoc && formDoc.crop_year) || null;
    if (!fid) { setError("No farmer on this row to look up a staged report for."); setPhase("review"); return; }
    try {
      const sf = await fetch("/api/market?fn=dropbox&action=stage-fillable", {
        method: "POST", headers: { ...getApiAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ farmer_id: fid, name: farmerName, crop_year: yr || undefined, state: formState || undefined }),
      });
      const sd = await sf.json().catch(() => ({}));
      if (!sf.ok || sd.ok === false || !sd.document_id) {
        throw new Error(sd.error || `Couldn't look up the staged report (HTTP ${sf.status}).`);
      }
      // Silas Filled is the outbox for every filled report — always aim the push there.
      if (sd.silasFilledDest) _lastFilledDest = sd.silasFilledDest;
      setFormId(sd.document_id);
      // Load the farmer's 578s so the agent can see the options and pick one or more.
      // Default the selection to the server's auto-pick (nationwide preferred).
      let selPaths = null;
      try {
        const l = await api("list-578s", { farmer_id: fid, crop_year: yr || undefined });
        setAvail578(l.list || []);
        const def = (l.defaultPaths && l.defaultPaths.length) ? l.defaultPaths
          : ((l.list && l.list.length) ? [l.list[0].path] : []);
        selPaths = new Set(def);
        setSel578(selPaths);
      } catch { setAvail578([]); }
      // A multi-policy farmer stages one form per policy (Archer/Stewart: MD + PA).
      // The board can't stamp both from one click, so surface a picker and fill each —
      // the SAME nationwide 578 feeds every form, only the form (its state's fields)
      // changes. One staged form → auto-preview as before.
      if (Array.isArray(sd.forms) && sd.forms.length > 1) {
        setForms(sd.forms);
        // Default to a form that still NEEDS filling, not the largest. Defaulting to
        // the largest is what silently re-ran Maryland on Stewart's re-fill while PA
        // sat unfilled; on a re-fill the pending state (PA) is what the agent wants.
        const pending = sd.forms.find((f) => !f.filled_at);
        setFormId((pending || sd.forms.find((f) => f.id === sd.document_id) || sd.forms[0]).id);
        setPhase("select");
        return;
      }
      doPreview(sd.document_id, selPaths);
    } catch (e) { setError(e.message); setPhase("review"); }
  }
  async function doPreview(selFormId, selPathsOverride) {
    setPhase("loading"); setError(null);
    try {
      const d = await api("preview", reqBody(selFormId, selPathsOverride));
      setReview(d.review); setPhase("review");
      // Share ≠ 100% on the 578 → pop a warning. The report is still filled at 100%
      // share (Kevin's convention), but the agent should confirm that's right for this
      // insured before sending it to be keyed.
      const sf = (d.review && d.review.exceptions && d.review.exceptions.shareNot100) || [];
      if (sf.length) {
        const who = sf.map((s) => `  • ${s.producer || "Operator"} — ${s.pct}%`).join("\n");
        window.alert(`⚠ Share is NOT 100% on the 578 for:\n\n${who}\n\nThe acreage report will still be filled at 100% share. Confirm this is intended for this insured before sending it for keying.`);
      }
      // Prevent-plant-only 578 → pop a warning. PP acres but no planted acres for the
      // insured crops usually means a PARTIAL 578 (planted portion is on a 578 we don't
      // have). Bainbridge got batch-filled off exactly this, dropping his planted acres.
      const ppo = d.review && d.review.exceptions && d.review.exceptions.ppOnly578;
      if (ppo) {
        window.alert(`⚠ This 578 has prevent-plant acres (${ppo.ppAcres} ac) but NO planted acres for the insured crops.\n\nIt's likely a prevent-plant-only partial — the planted acres may be on a 578 we don't have. Confirm the farmer is genuinely all prevent-plant, or upload the full 578 before filling.`);
      }
      // More than one 578 on file → the fill auto-picked one (nationwide preferred).
      // Pop a warning so a partial/single-county 578 can't be used unnoticed.
      const r578 = d.review && d.review.report578;
      // ⚠ THE SEASON IS NOW THE MOST DECISION-RELEVANT THING IN THIS LIST, because two
      // campaigns are live at once through the autumn. McIlroy holds a spring 2026
      // nationwide print beside a fall 2027 Livingston wheat print; told only the
      // filenames, an agent is guessing from "WheatFall2026".
      const desc = (x) => `${x.filename || "(unnamed)"}${x.season ? ` · ${x.season} ${x.taxYear || ""}`.trimEnd() : ""}${x.isNationwide ? " · NATIONWIDE" : x.county ? ` · ${x.county} only` : ""}`;
      // ⚠ READ seasonAsked OFF `r578`, NEVER off the `review` STATE. setReview() above
      // does not update this render's `review` binding, so `review` here is the PREVIOUS
      // preview — null the first time the modal is opened, which would throw inside the
      // very warning that exists to stop a wrong-season fill. check-undef cannot see it
      // (`review` resolves — it is state) and the build compiles, which is the
      // "throws at render" class CLAUDE.md records.
      const askedFor = String((r578 && r578.seasonAsked) || "").toUpperCase();
      if (r578 && r578.chosen && r578.chosen.seasonMismatch) {
        window.alert(`⚠ The only 578 on file for this farmer is a ${String(r578.chosen.season || "").toUpperCase()} one, and this is a ${askedFor} report.\n\nUsed:\n  • ${desc(r578.chosen)}\n\nA ${r578.chosen.season} 578 does not carry this campaign's crops, so little or nothing will match. Upload the ${askedFor.toLowerCase()} 578 before filling.`);
      }
      if (r578 && Array.isArray(r578.others) && r578.others.length) {
        const used = r578.chosen ? desc(r578.chosen) : "(unknown)";
        const rest = r578.others.map((o) => `  • ${desc(o)}`).join("\n");
        window.alert(`⚠ ${r578.others.length + 1} FSA-578s are on file for this farmer.\n\nUsed:\n  • ${used}\n\nAlso on file:\n${rest}\n\nConfirm the right 578 was used — a single-county 578 only covers that county's fields, and a spring 578 does not carry fall crops.`);
      }
    }
    catch (e) { setError(e.message); setPhase("review"); }
  }
  async function doFill() {
    setPhase("filling"); setError(null);
    try {
      const d = await api("fill", reqBody(formId || (formDoc && formDoc.id)));
      setReview(d.review || review);
      // EVERY deliberate refusal — practice ambiguity, unrecognised form, nothing
      // matched, parse didn't reconcile — comes back HTTP 200 with {ok:false,error}.
      // api() only throws on an HTTP error, so those used to fall straight through:
      // no PDF, no tab, no message. "It fills but nothing happens" (John B Martin,
      // 2026-07-22, blocked because his corn/beans/wheat are each insured under both
      // Non-Irrigated AND Organic). A refusal has to say why.
      if (d.ok === false) {
        setFilledUrl(null); setFilledPath(null); setPhase("review");
        setError(d.error || "The fill was refused, but no reason was given.");
        return;
      }
      setFilledUrl((d.filled && d.filled.signedUrl) || null); setFilledPath((d.filled && d.filled.path) || null); setPhase("review");
      if (d.filled && d.filled.signedUrl) window.open(d.filled.signedUrl, "_blank");
      else if (!d.filled) setError("The fill returned no document. Nothing was stamped.");
      // Tell the parent (the pipeline board) a fill just landed so it can re-read the
      // docs — otherwise the row keeps its pre-fill state and the "File" button never
      // appears until a manual page reload / re-scan (Bainbridge, 2026-07-29).
      if (d.filled && d.filled.signedUrl && onFilled) { try { onFilled(); } catch (_) {} }
    } catch (e) { setError(e.message); setPhase("review"); }
  }
  // Review-then-send: after the filled PDF is generated and the user has eyeballed
  // it, email it to Mandy (she keys it into the AIP). Uses the email-filled action.
  async function doEmailMandy() {
    if (!filledPath) return;
    setSent("sending"); setError(null);
    try {
      const d = await api("email-filled", { filled_path: filledPath, farmer_id: farmerId, farmer_name: farmerName, county });
      if (d.sent) setSent("sent"); else throw new Error(d.error || "email failed");
    } catch (e) { setError(e.message); setSent(null); }
  }

  const disc = (review && review.discrepancies) || [];
  // On a per-STATE fill, show the acres THIS state actually stamps (review.totals is the
  // scoped reconcile), not the whole-operation parse — otherwise a two-state farmer's PA
  // report reads 3,320 ac when only ~33 PA fields fill (Schofield, 2026-08-07). The
  // banner still reconciles the full parse; this table is "what gets stamped here".
  const perCrop = (review && review.formState && review.totals)
    ? Object.entries(review.totals).map(([crop, t]) => ({ crop, gotPlanted: (t && t.planted) || 0, gotPp: (t && t.pp) || 0, expPlanted: null, expPp: null, ok: true, scoped: true }))
    : (review && review.verify && review.verify.perCrop) || [];
  return (
    <>
      <button onClick={start} style={btnStyle || defaultBtn} title="Fill / QC-check the acreage report against the 578">{label}</button>
      {open && (
        <div onClick={close} style={{ position: "fixed", inset: 0, background: "rgba(20,30,20,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, maxWidth: 680, width: "100%", maxHeight: "86vh", overflow: "auto", padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0, color: "#1a2e1a", fontSize: 16 }}>Fill Acreage Report{farmerName ? ` — ${farmerName}` : ""}{formState ? ` · ${formState}` : ""}</h3>
              <button onClick={close} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#60735f" }}>✕</button>
            </div>

            {phase === "loading" && <div style={{ padding: 28, textAlign: "center", color: "#60735f" }}>Working… parsing the 578 and reconciling can take a few seconds.</div>}
            {phase === "filling" && <div style={{ padding: 28, textAlign: "center", color: "#60735f" }}>Stamping the acreage report…</div>}
            {error && <div style={{ padding: 12, background: "#fbeae7", color: "#a02c1a", borderRadius: 8, fontSize: 13, marginBottom: 10 }}>{error}</div>}

            {phase === "select" && !error && (
              <div>
                {forms.length === 0 ? (
                  <div style={{ padding: 12, background: "#fdf6e0", color: "#8a6a00", borderRadius: 8, fontSize: 13 }}>
                    No acreage report has been pulled in from Dropbox for this farmer yet. In File Management, click <b>Pull from Dropbox now</b>, then try again.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, color: "#3a4a38", marginBottom: 6 }}>
                      {forms.length > 1
                        ? <>This farmer has <b>{forms.length}</b> acreage reports (separate policies/states). Fill <b>each</b> one — pick a report, generate, then come back and pick the next.</>
                        : "Which acreage report should I fill?"}
                    </div>
                    <select value={formId} onChange={(e) => setFormId(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #c8d8c0", fontSize: 13, marginBottom: 14, boxSizing: "border-box" }}>
                      {forms.map((f) => (<option key={f.id} value={f.id}>{f.filled_at ? "✓ filled — " : "◻ needs fill — "}{f.policy_number ? `${f.policy_number} · ` : ""}{f.original_filename}{f.crop_year ? ` · CY${f.crop_year}` : ""}</option>))}
                    </select>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button onClick={close} style={acrModalBtn("1px solid #dde8d8", "#fff", "#60735f")}>Cancel</button>
                      <button disabled={!formId} onClick={() => doPreview(formId)} style={acrModalBtn("none", "#2d4a2d", "#fff")}>Check against 578 →</button>
                    </div>
                  </>
                )}
              </div>
            )}

            {phase === "review" && review && (
              <div>
                {!formDoc && forms.length > 1 && (
                  <div style={{ fontSize: 12, color: "#60735f", marginBottom: 8 }}>
                    Form: {((forms.find((f) => f.id === formId)) || {}).original_filename} · <button onClick={() => setPhase("select")} style={{ border: "none", background: "none", color: "#1f6b1a", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>change</button>
                  </div>
                )}
                {Array.isArray(avail578) && avail578.length > 0 && (
                  <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, background: "#f4f8f0", border: "1px solid #dde8d8" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#2d4a2d", marginBottom: avail578.length > 1 ? 6 : 2 }}>
                      📄 {avail578.length === 1 ? "FSA-578 on file" : `${avail578.length} FSA-578s on file — pick which to reconcile against`}
                    </div>
                    {avail578.length > 1 && (
                      <div style={{ fontSize: 11, color: "#60735f", marginBottom: 6 }}>Select one — or several if they together cover the operation (e.g. per-county 578s). Their fields are merged.</div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {avail578.map((d) => {
                        const on = sel578.has(d.path);
                        return (
                          <label key={d.path} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#3a4a38", cursor: avail578.length > 1 ? "pointer" : "default" }}>
                            {avail578.length > 1 && <input type="checkbox" checked={on} onChange={() => toggle578(d.path)} />}
                            <span style={{ fontWeight: on ? 700 : 500 }}>{d.filename || "(unnamed 578)"}</span>
                            {d.isNationwide
                              ? <span style={{ fontSize: 10, fontWeight: 700, color: "#1f6b1a", background: "#e3f3e0", padding: "1px 5px", borderRadius: 4 }}>NATIONWIDE</span>
                              : d.county ? <span style={{ fontSize: 10, color: "#8a6a30", background: "#fdf0e0", padding: "1px 5px", borderRadius: 4 }}>{d.county} only</span> : null}
                            {d.receivedAt && <span style={{ fontSize: 10, color: "#677167" }}>· {new Date(d.receivedAt).toLocaleDateString()}</span>}
                          </label>
                        );
                      })}
                    </div>
                    {avail578.length > 1 && (
                      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <button onClick={() => doPreview(formId || (formDoc && formDoc.id), sel578)} disabled={!sel578.size} style={{ ...acrModalBtn("none", sel578.size ? "#2d4a2d" : "#9bb39b", "#fff"), cursor: sel578.size ? "pointer" : "not-allowed", fontSize: 12, padding: "5px 10px" }}>↻ Re-check with selected</button>
                        <span style={{ fontSize: 11, color: "#60735f" }}>{sel578.size} selected{review.report578UsedCount != null ? ` · reconciled against ${review.report578UsedCount}` : ""}</span>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, fontWeight: 700, fontSize: 13, background: review.verify && review.verify.ok ? "#e3f3e0" : "#fbeae7", color: review.verify && review.verify.ok ? "#1f6b1a" : "#a02c1a" }}>
                  {review.verify && review.verify.ok ? "✓ Parse reconciles to the 578's own crop totals — acre for acre" : "⚠ Parse does NOT reconcile to the 578 totals — review before sending"}
                </div>
                {/* ⚠ A WRONG-SEASON 578 IS NOT A "CONFIRM", IT IS A WARNING. The picker
                    falls through to the other season rather than returning nothing — a
                    grower whose fall 578 has not arrived still has a form — but a spring
                    578 carries none of this campaign's crops, so almost nothing will
                    match and the result reads as a farm with no acreage. */}
                {review.report578 && review.report578.chosen && review.report578.chosen.seasonMismatch && (
                  <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, background: "#fdeaea", border: "1px solid #e8b0b0", color: "#8a2a2a" }}>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 3 }}>
                      ⚠ This is a {String(review.report578.seasonAsked || "").toUpperCase()} report and the only 578 on file is a {String(review.report578.chosen.season || "").toUpperCase()} one
                    </div>
                    <div style={{ fontSize: 12, color: "#9a4a4a" }}>
                      Used: <b>{review.report578.chosen.filename || "(unnamed)"}</b>
                      {review.report578.chosen.taxYear ? ` · ${review.report578.chosen.season} ${review.report578.chosen.taxYear}` : ""}
                      . A {review.report578.chosen.season} 578 does not carry this campaign's crops — upload the {review.report578.seasonAsked} 578 before filling.
                    </div>
                  </div>
                )}
                {review.report578 && Array.isArray(review.report578.others) && review.report578.others.length > 0 && (
                  <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, background: "#fdf0e0", border: "1px solid #ecc9a0", color: "#8a3a00" }}>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 3 }}>⚠ {review.report578.others.length + 1} FSA-578s on file — confirm the right one was used</div>
                    <div style={{ fontSize: 12, color: "#7a5a30" }}>
                      Used: <b>{(review.report578.chosen && review.report578.chosen.filename) || "(unnamed)"}</b>
                      {review.report578.chosen && review.report578.chosen.season ? ` · ${review.report578.chosen.season} ${review.report578.chosen.taxYear || ""}`.trimEnd() : ""}
                      {review.report578.chosen && (review.report578.chosen.isNationwide ? " · nationwide" : review.report578.chosen.county ? ` · ${review.report578.chosen.county} only` : "")}
                    </div>
                    <div style={{ fontSize: 11, color: "#a06a30", marginTop: 3 }}>
                      Also on file: {review.report578.others.map((o, i) => (
                        <span key={i}>{(o.filename || "(unnamed)")}{o.season ? ` (${o.season} ${o.taxYear || ""})`.replace(" )", ")") : ""}{o.isNationwide ? " (nationwide)" : o.county ? ` (${o.county} only)` : ""}{i < review.report578.others.length - 1 ? "; " : ""}</span>
                      ))}. A single-county 578 only covers that county's fields, and a spring 578 does not carry fall crops.
                    </div>
                  </div>
                )}
                {Array.isArray(review.exceptions?.shareNot100) && review.exceptions.shareNot100.length > 0 && (
                  <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, background: "#fdf0e0", border: "1px solid #ecc9a0", color: "#8a3a00" }}>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 3 }}>⚠ Share is not 100% on the 578</div>
                    <div style={{ fontSize: 12, color: "#7a5a30" }}>
                      {review.exceptions.shareNot100.map((s, i) => (
                        <span key={i}>{s.producer || "Operator"}: <b>{s.pct}%</b>{i < review.exceptions.shareNot100.length - 1 ? " · " : ""}</span>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: "#a06a30", marginTop: 4 }}>The acreage report is still filled at <b>100% share</b>. Confirm that's intended for this insured before sending.</div>
                  </div>
                )}
                {review.exceptions?.ppOnly578 && (
                  <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, background: "#fdece0", border: "1px solid #e0b48a", color: "#8a3a00" }}>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 3 }}>⚠ Prevent-plant-only 578 — planted acres may be missing</div>
                    <div style={{ fontSize: 12, color: "#7a5a30" }}>
                      This 578 has <b>{review.exceptions.ppOnly578.ppAcres} ac</b> prevent-plant but <b>no planted acres</b> for the insured crops.
                    </div>
                    <div style={{ fontSize: 11, color: "#a06a30", marginTop: 4 }}>Usually a partial 578 — the planted acres are on a 578 we don't have. Confirm the farmer is genuinely all prevent-plant, or upload the full 578 before filling.</div>
                  </div>
                )}
                {review.formRecognized === false && (
                  <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, fontWeight: 700, fontSize: 13, background: "#fdf0e0", color: "#8a3a00" }}>
                    ⚠ This acreage report's layout wasn't recognized (0 fields read), so nothing can be stamped. R&amp;H and NAU forms are both supported — this is likely a scan/format issue with this particular file.
                  </div>
                )}
                {Array.isArray(review.fillCrops) && (
                  <div style={{ fontSize: 12, color: "#60735f", marginBottom: 10 }}>
                    Insured spring crops to fill: <b>{review.fillCrops.join(", ") || "(none)"}</b>{review.insuredCrops ? "" : " · no coverage data found, using default corn/soy"}
                  </div>
                )}
                {review.formState && (
                  <div style={{ fontSize: 11.5, color: "#1a4a7a", background: "#eef2fb", border: "1px solid #d6e0f2", borderRadius: 8, padding: "8px 11px", marginBottom: 12 }}>
                    ℹ Acres below are the <b>{review.formState}</b> total this report stamps{review.otherStateFields ? ` — ${review.otherStateFields} field(s) in ${(review.otherStates || []).join("/") || "other states"} go on their own report` : ""}. The reconcile check (✓/✗ badge) still validates the whole nationwide 578 so the full parse is verified; there's no per-state printed total to compare against, so the “/ 578” column shows “—”.
                  </div>
                )}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 14 }}>
                  <thead><tr style={{ textAlign: "left", color: "#60735f", fontSize: 11 }}><th style={{ padding: "2px 0" }}>Crop</th><th>Planted parsed / 578</th><th>PP parsed / 578</th><th></th></tr></thead>
                  <tbody>{perCrop.map((c) => (
                    <tr key={c.crop} style={{ borderTop: "1px solid #eef3ec" }}>
                      <td style={{ padding: "5px 0", fontWeight: 700 }}>{c.crop}</td>
                      <td>{Number(c.gotPlanted).toFixed(2)} / {c.expPlanted == null ? "—" : Number(c.expPlanted).toFixed(2)}</td>
                      <td>{Number(c.gotPp).toFixed(2)} / {c.expPp == null ? "—" : Number(c.expPp).toFixed(2)}</td>
                      <td style={{ color: c.unverified ? "#8a6a00" : c.ok ? "#1f6b1a" : "#a02c1a", fontWeight: 800 }} title={c.scoped ? "State-scoped acres — the full parse is reconciled by the badge below/above, not per crop here" : c.unverified ? "The 578's printed total for this crop wasn't captured — trusting the line-item rows" : ""}>{c.scoped ? "" : c.unverified ? "≈" : c.ok ? "✓" : "✗"}</td>
                    </tr>
                  ))}</tbody>
                </table>
                {Array.isArray(review.verify?.unverified) && review.verify.unverified.length > 0 && (
                  <div style={{ fontSize: 11, color: "#8a6a00", marginTop: -8, marginBottom: 12 }}>
                    ≈ {review.verify.unverified.join(", ")}: the 578's printed operation total wasn't captured this parse, so these were checked against the line-item rows (which parse stably) rather than the printed summary.
                  </div>
                )}
                <div style={{ fontWeight: 800, fontSize: 13, color: disc.length ? "#a02c1a" : "#1f6b1a", marginBottom: 6 }}>
                  {disc.length ? `⚠ ${disc.length} insurable field(s) on the 578 with NO row on the acreage report` : "✓ No missing insurable fields"}
                </div>
                {disc.length > 0 && (
                  <ul style={{ margin: "0 0 14px", paddingLeft: 18, fontSize: 13, color: "#3a4a38" }}>
                    {disc.map((x, i) => (<li key={i}>F{x.farm}-T{x.tract}-{x.field} · {x.crop} · planted {(+x.plantedAcres || 0).toFixed(2)} ac / PP {(+x.ppAcres || 0).toFixed(2)} ac</li>))}
                  </ul>
                )}
                {Array.isArray(review.mixedUse) && review.mixedUse.length > 0 && (
                  <div style={{ padding: "10px 12px", background: "#fdf6e0", border: "1px solid #ecdfae", borderRadius: 8, marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#8a6a00", marginBottom: 2 }}>⚠ The 578 reports both grain and silage — how should we key it?</div>
                    <div style={{ fontSize: 11, color: "#7a6a40", marginBottom: 8 }}>Your call — the insurance keying doesn't have to match the 578.</div>
                    {review.mixedUse.map((m) => (
                      <div key={m.crop} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5, fontSize: 13, color: "#3a4a38", flexWrap: "wrap" }}>
                        <b style={{ minWidth: 60 }}>{m.crop}</b>
                        <span style={{ fontSize: 11, color: "#60735f", minWidth: 150 }}>578: <b>{fmt(m.grainAcres, 1)} ac grain</b> · <b>{fmt(m.silageAcres, 1)} ac silage</b></span>
                        <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                          <input type="radio" name={`tc-${m.crop}`} checked={typeChoices[m.crop] === "reported"} onChange={() => pickType(m.crop, "reported")} /> As reported <span style={{ color: "#60735f" }}>(per field)</span>
                        </label>
                        <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                          <input type="radio" name={`tc-${m.crop}`} checked={typeChoices[m.crop] === "grain"} onChange={() => pickType(m.crop, "grain")} /> All as grain <span style={{ color: "#60735f" }}>({m.grainText})</span>
                        </label>
                        {m.silageText && (
                          <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                            <input type="radio" name={`tc-${m.crop}`} checked={typeChoices[m.crop] === "silage"} onChange={() => pickType(m.crop, "silage")} /> All as silage <span style={{ color: "#60735f" }}>({m.silageText})</span>
                          </label>
                        )}
                      </div>
                    ))}
                    {review.mixedUse.some((m) => !typeChoices[m.crop])
                      ? <div style={{ fontSize: 11, color: "#c0392b", fontWeight: 700, marginTop: 4 }}>Pick how to key each crop above before generating.</div>
                      : filledUrl && <div style={{ fontSize: 11, color: "#8a6a00", marginTop: 4 }}>Choice changed — click <b>Generate filled form</b> again to re-stamp.</div>}
                  </div>
                )}
                <div style={{ fontSize: 12, color: "#60735f", marginBottom: 14 }}>{review.stampCount} field row(s) will be filled. Spring crops only (corn / soybeans / dry and proc beans); wheat, grass, idle and cover crops are left blank.</div>
                {(() => {
                  const needChoice = Array.isArray(review.mixedUse) && review.mixedUse.some((m) => !typeChoices[m.crop]);
                  const blocked = review.formRecognized === false || needChoice;
                  return (
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button onClick={close} style={acrModalBtn("1px solid #dde8d8", "#fff", "#60735f")}>Close</button>
                  <button onClick={doFill} disabled={blocked} title={needChoice ? "Choose grain/silage keying above first" : ""} style={{ ...acrModalBtn("none", blocked ? "#9bb39b" : "#2d4a2d", "#fff"), cursor: blocked ? "not-allowed" : "pointer" }}>🌾 Generate filled form</button>
                </div>
                  );
                })()}
                {filledUrl && (
                  <div style={{ marginTop: 12, padding: "12px 14px", background: "#f4f8f0", border: "1px solid #dde8d8", borderRadius: 8 }}>
                    <a href={filledUrl} target="_blank" rel="noreferrer" style={{ color: "#1f6b1a", fontWeight: 700, fontSize: 13 }}>↓ Open the filled acreage report to review</a>
                    <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      {sent === "sent" ? (
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#1f6b1a" }}>✓ Sent to Mandy</span>
                      ) : (
                        <button onClick={doEmailMandy} disabled={sent === "sending"} style={{ ...acrModalBtn("none", sent === "sending" ? "#9bb39b" : "#2d4a2d", "#fff"), cursor: sent === "sending" ? "wait" : "pointer" }}>
                          {sent === "sending" ? "Sending…" : "📧 Send to Mandy"}
                        </button>
                      )}
                      <span style={{ fontSize: 11, color: "#60735f" }}>Review the PDF first — this emails it to Mandy to key into the AIP.</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function DocumentHubPanel({ farmers }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [dbx, setDbx] = useState(null);       // Dropbox connection status
  const [pulling, setPulling] = useState(false);
  const [picker, setPicker] = useState(null); // folder browser: null=closed, else {path,folders,pdfCount,loading}
  const [importingContacts, setImportingContacts] = useState(false);
  const [statusFilter, setStatusFilter] = useState("needs"); // needs | sent | signed | all
  const [sourcesOpen, setSourcesOpen] = useState(false);     // collapse Dropbox/email plumbing
  const [addOpen, setAddOpen] = useState(false);             // collapse the upload dropzone
  const [menuOpenId, setMenuOpenId] = useState(null);        // per-row ⋯ action menu
  const [toggled, setToggled] = useState(new Set());         // farmer groups toggled away from the per-tab default
  const fileRef = useRef(null);

  const farmersById = useMemo(() => {
    const m = {}; (farmers || []).forEach((f) => { if (f?.id) m[f.id] = f; }); return m;
  }, [farmers]);

  const addLog = (msg, type = "info") =>
    setLog((prev) => [...prev.slice(-50), { ts: new Date().toLocaleTimeString("en-US", { hour12: false }), msg, type }]);

  useEffect(() => { loadDocs(); loadDbxStatus(); }, []);
  // Reset per-group expand/collapse overrides when the status tab changes, so each
  // tab falls back to its default (All = collapsed, workflow tabs = expanded).
  useEffect(() => { setToggled(new Set()); setMenuOpenId(null); }, [statusFilter]);
  async function loadDocs() {
    setLoading(true);
    try {
      const rows = await sbFetch("farmer_documents");
      setDocs((rows || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (e) { console.error("load farmer_documents:", e); }
    setLoading(false);
  }

  async function loadDbxStatus() {
    try {
      const r = await fetch("/api/market?fn=dropbox&action=status", { headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => null);
      setDbx(r.ok ? d : null);
    } catch { setDbx(null); }
  }
  // Token in a HEADER, never in the address (blueprint B19) — same as Box connect().
  async function connectDropbox() {
    const auth = getApiAuthHeaders().Authorization;
    if (!auth) { window.alert("No session token found — please re-login and try again."); return; }
    const popup = window.open("", "dropbox-oauth", "width=560,height=720");
    if (!popup) { window.alert("Popup blocked — allow popups for this site."); return; }
    try {
      const r = await adminApiFetch("/api/market?fn=dropbox&action=oauth-start&format=json", { credentials: "same-origin" });
      const d = await r.json().catch(() => null);
      if (!r.ok || !d || typeof d.url !== "string" || d.url.indexOf("https://www.dropbox.com/") !== 0) {
        throw new Error((d && d.error) || `Dropbox connect failed (HTTP ${r.status})`);
      }
      popup.location.href = d.url;
    } catch (e) {
      try { popup.close(); } catch (e2) { /* already closed */ }
      window.alert(String((e && e.message) || e)); return;
    }
    const onMsg = (e) => { if (e.data === "dropbox-connected") { window.removeEventListener("message", onMsg); setTimeout(loadDbxStatus, 800); } };
    window.addEventListener("message", onMsg);
  }
  async function pullNow() {
    setPulling(true);
    try {
      const r = await fetch("/api/market?fn=dropbox&action=sync", { method: "POST", headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.error === "string" ? d.error : JSON.stringify(d).slice(0, 200));
      addLog(`Dropbox pull: ${d.new || 0} new, ${d.scanned || 0} scanned${d.skipped_existing ? `, ${d.skipped_existing} already filed` : ""}`, (d.new || 0) > 0 ? "success" : "info");
      await loadDocs(); await loadDbxStatus();
    } catch (e) { addLog(`Dropbox pull failed: ${e.message}`, "error"); }
    setPulling(false);
  }
  async function importContacts() {
    setImportingContacts(true);
    try {
      const r = await fetch("/api/market?fn=dropbox-sign&action=contacts-import", { method: "POST", headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.error === "string" ? d.error : (d.detail || JSON.stringify(d)).slice(0, 200));
      addLog(`Imported ${d.farmers_updated || 0} farmer email${d.farmers_updated === 1 ? "" : "s"} from Dropbox Sign (${d.contacts_found || 0} contacts found).`, (d.farmers_updated || 0) > 0 ? "success" : "info");
    } catch (e) { addLog(`Contact import failed: ${e.message}`, "error"); }
    setImportingContacts(false);
  }
  // mode: "watch" = set the pull folder; "dest" = choose where a signed copy goes
  // (doc carried through so we can open the editor after the folder is picked).
  async function openPicker(path = "", mode = "watch", doc = null) {
    setPicker((p) => ({ path, folders: (p && p.folders) || [], pdfCount: 0, loading: true, error: null, mode: (p && p.mode) || mode, doc: (p && p.doc) || doc }));
    try {
      const r = await fetch(`/api/market?fn=dropbox&action=list-folders&path=${encodeURIComponent(path)}`, { headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setPicker((p) => ({ path: d.path != null ? d.path : path, folders: d.folders || [], pdfCount: d.pdf_count || 0, loading: false, error: null, mode: (p && p.mode) || mode, doc: (p && p.doc) || doc }));
    } catch (e) {
      addLog(`Browse failed: ${e.message}`, "error");
      setPicker((p) => ({ path: (p && p.path) || path, folders: (p && p.folders) || [], pdfCount: 0, loading: false, error: e.message, mode: (p && p.mode) || mode, doc: (p && p.doc) || doc }));
    }
  }
  async function chooseFolder(path) {
    try {
      const r = await fetch(`/api/market?fn=dropbox&action=set-folder&path=${encodeURIComponent(path)}`, { method: "POST", headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "failed");
      addLog(`Watch folder set to ${d.watch_folder || path}`, "success");
      setPicker(null);
      await loadDbxStatus();
    } catch (e) { addLog(`Set folder failed: ${e.message}`, "error"); }
  }
  // Destination chosen for a signed copy → remember it, then open the box editor.
  async function chooseDest(path, doc) {
    _lastSignedDest = path;
    setPicker(null);
    await openEditorForDoc(doc, path);
  }

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []).filter((f) => /\.pdf$/i.test(f.name));
    if (!files.length) { addLog("No PDF files selected", "warn"); return; }
    setBusy(true);
    for (const file of files) {
      try {
        addLog(`Processing ${file.name}…`, "info");
        const candidates = parsePolicyCandidates(file.name);
        let policyRow = null;
        if (candidates.length) {
          const found = await sbFetch("policies", {
            select: "id,farmer_id,policy_number,crop_year",
            filters: [`policy_number=in.(${candidates.join(",")})`],
          });
          policyRow = (found || [])[0] || null;
        }
        const farmerId = policyRow?.farmer_id || null;
        const yrMatch = file.name.match(/-(\d{2})\b/);
        const cropYear = policyRow?.crop_year || (yrMatch ? 2000 + parseInt(yrMatch[1], 10) : null);
        const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const storagePath = `documents/${farmerId || "unmatched"}/${ts}_${safe}`;
        await sbStorageUpload("farmer-documents", storagePath, file);
        const filedDoc = await sbInsert("farmer_documents", [{
          farmer_id: farmerId,
          policy_id: policyRow?.id || null,
          policy_number: policyRow?.policy_number || candidates[0] || null,
          doc_type: inferDocType(file.name),
          crop_year: cropYear,
          title: file.name.replace(/\.pdf$/i, ""),
          original_filename: file.name,
          source: "manual_upload",
          storage_path: storagePath,
          matched_by: policyRow ? "policy_number" : null,
        }]);
        addLog(
          `✓ ${file.name} → ${farmerId ? (farmersById[farmerId]?.name || "matched farmer") : "UNMATCHED (no policy # in filename)"}`,
          farmerId ? "success" : "warn"
        );
        // A 578 filed here was invisible to the 578 audit and the season boards (they
        // read farmer_578s). Hand it to the same intake the Box sync runs — the stored
        // file is reused, not uploaded again, and the intake sends Mandy the one email.
        const filedDocId = Array.isArray(filedDoc) && filedDoc[0] ? filedDoc[0].id : null;
        if (inferDocType(file.name) === "578" && filedDocId) {
          try {
            const r = await fetch(`/api/box?action=ingest-document&id=${filedDocId}`, { method: "POST", headers: getApiAuthHeaders() });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
            const outcomeKey = d.decision === "import" && !d.emailed ? "import_unsent" : d.decision;
            addLog(STORED_578_OUTCOME[outcomeKey] ? `${file.name}: ${STORED_578_OUTCOME[outcomeKey]}` : `${file.name}: 578 intake answered "${d.decision}"`,
              d.decision === "import" ? "success" : "warn");
          } catch (e) { addLog(`${file.name}: filed as a document, but the 578 intake failed: ${e.message}`, "warn"); }
        }
      } catch (e) { addLog(`✗ ${file.name}: ${e.message}`, "error"); }
    }
    setBusy(false);
    await loadDocs();
  }

  // Step 1: choose where the SIGNED copy should land in Dropbox (every send),
  // then step 2 opens the box editor. dbx not connected → skip straight to editor.
  function sendForSignature(doc) {
    if (dbx?.connected) openPicker(_lastSignedDest || "", "dest", doc);
    else openEditorForDoc(doc, null);
  }
  // Create the embedded draft (with the signed-copy destination), then launch
  // Dropbox Sign's field editor IN-APP — drag signature/date boxes, then send.
  // Status + the signed PDF bind back via the callback (metadata=document_id).
  async function openEditorForDoc(doc, signedDestPath) {
    try {
      const r = await fetch("/api/market?fn=dropbox-sign&action=create-embedded-draft", {
        method: "POST",
        headers: getApiAuthHeaders(),
        body: JSON.stringify({ document_id: doc.id, signed_dest_path: signedDestPath || null }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data.error === "string" ? data.error : (data.detail || JSON.stringify(data)).slice(0, 300));
      const HelloSign = await loadHelloSignEmbedded();
      const client = new HelloSign({ clientId: data.client_id });
      client.open(data.claim_url, { testMode: !!data.test_mode, skipDomainVerification: !!data.test_mode });
      client.on("send", async () => {
        addLog(`Sent "${doc.title}" for signature${data.test_mode ? " (test mode)" : ""}${signedDestPath ? ` → signed copy will go to ${signedDestPath}` : ""}.`, "success");
        // Mark sent right away so the button stops showing "Set up & send" (which
        // otherwise tempts a re-click). The status callback lands a few seconds
        // later and fills in the signature_request_id.
        setDocs((prev) => prev.map((x) => (x.id === doc.id ? { ...x, signature_status: "sent", sent_for_signature_at: new Date().toISOString() } : x)));
        try {
          await fetch(`${SUPABASE_URL}/rest/v1/farmer_documents?id=eq.${doc.id}`, {
            method: "PATCH", headers: { ...getAuthHeaders(), Prefer: "return=minimal" },
            body: JSON.stringify({ signature_status: "sent", sent_for_signature_at: new Date().toISOString() }),
          });
        } catch { /* callback will still set it */ }
      });
      // Reload after a short delay so the async status callback (request id) lands first.
      client.on("close", () => { setTimeout(() => loadDocs(), 3500); });
      client.on("error", (e) => addLog(`Signature editor error: ${(e && (e.code || e.message)) || "unknown"}`, "error"));
    } catch (e) { window.alert("Could not open the signature editor: " + e.message); }
  }

  async function viewPdf(path) {
    try { const url = await sbStorageSignedUrl("farmer-documents", path); window.open(url, "_blank"); }
    catch (e) { window.alert("Failed to open PDF: " + e.message); }
  }
  async function deleteDoc(id) {
    if (!window.confirm("Delete this document record? (The stored PDF is left in place.)")) return;
    try { await sbDelete("farmer_documents", `id=eq.${id}`); setDocs((prev) => prev.filter((d) => d.id !== id)); }
    catch (e) { window.alert("Delete failed: " + e.message); }
  }
  // Mark signed manually — for docs the grower signed on paper or the iPad
  // (outside Dropbox Sign) so they don't hang in the pending list.
  async function markSignedManually(doc) {
    if (!window.confirm(`Mark "${doc.title}" as signed?\n\nUse this when the grower signed on paper or your iPad (outside Dropbox Sign). It moves out of the pending list.`)) return;
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/farmer_documents?id=eq.${doc.id}`, {
        method: "PATCH", headers: { ...getAuthHeaders(), Prefer: "return=minimal" },
        body: JSON.stringify({ signature_status: "signed", signed_at: new Date().toISOString(), notes: "Signed offline (paper/iPad)", updated_at: new Date().toISOString() }),
      });
      setDocs((prev) => prev.map((x) => (x.id === doc.id ? { ...x, signature_status: "signed", signed_at: new Date().toISOString() } : x)));
      addLog(`Marked "${doc.title}" as signed (offline).`, "success");
    } catch (e) { window.alert("Mark signed failed: " + e.message); }
  }

  function statusBadge(d) {
    const map = {
      none: { bg: "#eef2ee", fg: "#5a7458", label: "—" },
      sent: { bg: "#fff3d6", fg: "#7a5a00", label: "📤 Sent for signature" },
      signed: { bg: "#e3f3e0", fg: "#1f6b1a", label: "✅ Signed" },
      // Signed in Dropbox Sign, but the executed PDF could not be fetched/stored.
      // "Signed" means we hold the copy, so this is its own state.
      signed_copy_missing: { bg: "#fff3d6", fg: "#7a5a00", label: "⚠ Signed — copy not on file" },
      declined: { bg: "#fde6e2", fg: "#a02c1a", label: "✖ Declined" },
      error: { bg: "#fde6e2", fg: "#a02c1a", label: "⚠ Send error" },
      draft: { bg: "#eef2ee", fg: "#5a7458", label: "Draft" },
    };
    const s = map[d.signature_status] || map.none;
    return <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 5, background: s.bg, color: s.fg }}>{s.label}</span>;
  }

  const statusOf = (d) => (d.signature_status === "signed" || d.signature_status === "signed_copy_missing" ? "signed" : d.signature_status === "sent" ? "sent" : "needs");
  const typeFiltered = typeFilter === "all" ? docs : docs.filter((d) => d.doc_type === typeFilter);
  const statusCounts = { all: typeFiltered.length, needs: 0, sent: 0, signed: 0 };
  typeFiltered.forEach((d) => { statusCounts[statusOf(d)]++; });
  const shown = statusFilter === "all" ? typeFiltered : typeFiltered.filter((d) => statusOf(d) === statusFilter);
  // Group by farmer (unmatched last).
  const groups = useMemo(() => {
    const g = new Map();
    for (const d of shown) {
      const key = d.farmer_id || "__unmatched__";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(d);
    }
    return [...g.entries()].sort((a, b) => {
      if (a[0] === "__unmatched__") return 1;
      if (b[0] === "__unmatched__") return -1;
      return (farmersById[a[0]]?.name || "").localeCompare(farmersById[b[0]]?.name || "");
    });
  }, [shown, farmersById]);

  const cellTd = { padding: "9px 12px", fontSize: 12.5, color: "#1a2e1a", borderBottom: "1px solid #eef2ee", verticalAlign: "middle" };
  const toolBtn = (active) => ({ fontSize: 12, fontWeight: 700, padding: "6px 12px", borderRadius: 7, cursor: "pointer", border: "1px solid " + (active ? "#2d4a2d" : "#c8d8c0"), background: active ? "#2d4a2d" : "#fff", color: active ? "#fff" : "#4a6e4a" });
  const srcBtn = { fontSize: 12, fontWeight: 700, padding: "7px 12px", borderRadius: 7, border: "1px solid #c8d8c0", background: "#fff", color: "#4a6e4a", cursor: "pointer" };
  const srcBtnDark = { fontSize: 12, fontWeight: 800, padding: "7px 14px", borderRadius: 7, border: "none", background: "#2d4a2d", color: "#fff", cursor: "pointer" };
  const rowAct = (border, bg, color) => ({ fontSize: 11, fontWeight: 700, padding: "5px 10px", marginLeft: 6, borderRadius: 5, border, background: bg, color, cursor: "pointer" });
  const STATUS_TABS = [
    { key: "needs", label: "Needs signature" },
    { key: "sent", label: "Sent" },
    { key: "signed", label: "Signed" },
    { key: "all", label: "All" },
  ];
  // Default-open rule: workflow tabs expanded, "All" collapsed for a clean library
  // view. `toggled` holds per-group exceptions (reset on tab change above).
  const defaultOpen = statusFilter !== "all";
  const isGroupOpen = (fid) => (toggled.has(fid) ? !defaultOpen : defaultOpen);
  const toggleGroup = (fid) => setToggled((prev) => { const n = new Set(prev); n.has(fid) ? n.delete(fid) : n.add(fid); return n; });
  const allOpen = () => setToggled(defaultOpen ? new Set() : new Set(groups.map((g) => g[0])));
  const allClosed = () => setToggled(defaultOpen ? new Set(groups.map((g) => g[0])) : new Set());

  return (
    <div>
      {/* Compact toolbar — plumbing tucked behind toggles */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <button onClick={() => setSourcesOpen((o) => !o)} style={toolBtn(sourcesOpen)}>⚙ Sources <span style={{ opacity: 0.85 }}>{dbx?.connected ? "🟢" : "⚪"}</span></button>
        <button onClick={() => setAddOpen((o) => !o)} style={toolBtn(addOpen)}>＋ Add files</button>
        {dbx?.connected && (
          <button onClick={pullNow} disabled={pulling} style={toolBtn(false)}>{pulling ? "Pulling…" : "📥 Pull"}</button>
        )}
        <button onClick={importContacts} disabled={importingContacts} title="Pull signer emails saved in Dropbox Sign into farmers so sends auto-fill" style={toolBtn(false)}>{importingContacts ? "Importing…" : "🔄 Import emails"}</button>
      </div>

      {/* Sources panel (collapsible) */}
      {sourcesOpen && (
        <div style={{ marginBottom: 14, padding: "12px 14px", background: "#eef5ea", border: "1px solid #d4e4cc", borderRadius: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16 }}>🗂️</span>
          {dbx?.connected ? (
            <>
              <div style={{ fontSize: 12, color: "#2d4a2d" }}>
                <b>Dropbox connected</b>{dbx.email ? ` · ${dbx.email}` : ""}<br />
                <span style={{ color: "#60735f" }}>Watching: {dbx.watchFolder}{dbx.lastSyncAt ? ` · last pull ${new Date(dbx.lastSyncAt).toLocaleString()}` : " · never pulled"}</span>
              </div>
              <button onClick={() => openPicker("")} style={{ ...srcBtn, marginLeft: "auto" }}>📁 Watch folder</button>
              <button onClick={pullNow} disabled={pulling} style={srcBtnDark}>{pulling ? "Pulling…" : "📥 Pull now"}</button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, color: "#2d4a2d" }}><b>Dropbox not connected</b><br /><span style={{ color: "#60735f" }}>Connect to auto-pull the “Needs Signed” folder.</span></div>
              <button onClick={connectDropbox} style={{ ...srcBtnDark, marginLeft: "auto" }}>Connect Dropbox</button>
            </>
          )}
        </div>
      )}

      {/* Folder browser modal */}
      {picker && (
        <div onClick={() => setPicker(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "min(560px,92vw)", maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #e3ece0", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>🗂️</span>
              <b style={{ fontSize: 14, color: "#2d4a2d" }}>{picker.mode === "dest" ? "Choose where the SIGNED copy goes" : "Choose Dropbox folder to watch"}</b>
              <button onClick={() => setPicker(null)} style={{ marginLeft: "auto", border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "#60735f" }}>✕</button>
            </div>
            <div style={{ padding: "8px 18px", fontSize: 12, color: "#60735f", borderBottom: "1px solid #eef2ee", wordBreak: "break-all" }}>
              📍 {picker.path || "/ (Dropbox root)"}
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {picker.path !== "" && (
                <button onClick={() => openPicker(picker.path.split("/").slice(0, -1).join("/"), picker.mode, picker.doc)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 18px", border: "none", borderBottom: "1px solid #f3f6f2", background: "transparent", cursor: "pointer", fontSize: 13, color: "#4a6e4a" }}>⬆️ Up one level</button>
              )}
              {picker.loading ? (
                <div style={{ padding: 18, color: "#60735f", fontSize: 13 }}>Loading…</div>
              ) : picker.folders.length === 0 ? (
                <div style={{ padding: 18, color: "#60735f", fontSize: 13 }}>No subfolders here{picker.pdfCount ? ` · ${picker.pdfCount} PDFs` : ""}.</div>
              ) : (
                picker.folders.map((f) => (
                  <div key={f.path} style={{ display: "flex", alignItems: "center", padding: "8px 18px", borderBottom: "1px solid #f3f6f2" }}>
                    <button onClick={() => openPicker(f.path, picker.mode, picker.doc)} style={{ flex: 1, textAlign: "left", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: "#1a2e1a" }}>📁 {f.name}</button>
                    <button onClick={() => (picker.mode === "dest" ? chooseDest(f.path, picker.doc) : chooseFolder(f.path))} style={{ fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 5, border: "1px solid #2d4a2d", background: "#fff", color: "#2d4a2d", cursor: "pointer" }}>Select</button>
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: "12px 18px", borderTop: "1px solid #e3ece0", display: "flex", gap: 10, alignItems: "center" }}>
              {picker.path !== "" && (
                <button onClick={() => (picker.mode === "dest" ? chooseDest(picker.path, picker.doc) : chooseFolder(picker.path))} style={{ fontSize: 12, fontWeight: 800, padding: "8px 14px", borderRadius: 7, border: "none", background: "#2d4a2d", color: "#fff", cursor: "pointer" }}>
                  {picker.mode === "dest" ? "✓ Send signed copy here" : `✓ Use this folder${picker.pdfCount ? ` (${picker.pdfCount} PDFs)` : ""}`}
                </button>
              )}
              <button onClick={() => setPicker(null)} style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, padding: "8px 14px", borderRadius: 7, border: "1px solid #c8d8c0", background: "#fff", color: "#4a6e4a", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add files (collapsible dropzone) */}
      {addOpen && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{ border: `2px dashed ${dragOver ? "#2d4a2d" : "#c8d8c0"}`, borderRadius: 12, padding: "22px 20px", textAlign: "center", background: dragOver ? "#eef5ea" : "#f8faf7", cursor: "pointer", marginBottom: 14 }}>
          <input ref={fileRef} type="file" accept="application/pdf" multiple style={{ display: "none" }} onChange={(e) => handleFiles(e.target.files)} />
          <div style={{ fontSize: 22, marginBottom: 4 }}>📄</div>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "#2d4a2d" }}>{busy ? "Uploading…" : "Drop Acreage Reports / Schedules of Insurance"}</div>
          <div style={{ fontSize: 11, color: "#60735f", marginTop: 3 }}>Auto-filed by farmer using the policy number in the filename.</div>
        </div>
      )}

      {/* Activity log */}
      {log.length > 0 && (
        <div style={{ background: "#1a2e1a", borderRadius: 8, padding: "10px 14px", marginBottom: 14, maxHeight: 120, overflowY: "auto", fontFamily: "ui-monospace,Menlo,monospace" }}>
          {log.map((l, i) => (
            <div key={i} style={{ fontSize: 11, color: l.type === "error" ? "#ff9b8a" : l.type === "warn" ? "#ffd27a" : l.type === "success" ? "#a8e89a" : "#cdd8cb", lineHeight: 1.6 }}>
              <span style={{ opacity: 0.55 }}>{l.ts}</span> {l.msg}
            </div>
          ))}
        </div>
      )}

      {/* Status tabs — primary workflow filter (mirrors the NEED SIGNED → SIGNED & READY flow) */}
      <div style={{ display: "flex", gap: 4, marginBottom: 10, borderBottom: "1px solid #e3ece0", flexWrap: "wrap" }}>
        {STATUS_TABS.map((t) => (
          <button key={t.key} onClick={() => { setStatusFilter(t.key); setMenuOpenId(null); }}
            style={{ fontSize: 13, fontWeight: 800, padding: "8px 14px", border: "none", borderBottom: "2px solid " + (statusFilter === t.key ? "#2d4a2d" : "transparent"), background: "transparent", color: statusFilter === t.key ? "#1a2e1a" : "#60735f", cursor: "pointer" }}>
            {t.label} <span style={{ fontWeight: 700, color: statusFilter === t.key ? "#2d6a1a" : "#aab8a6" }}>{statusCounts[t.key]}</span>
          </button>
        ))}
      </div>

      {/* Type filter — secondary */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {["all", "acreage_report", "schedule_of_insurance", "578", "other"].map((t) => (
          <button key={t} onClick={() => setTypeFilter(t)}
            style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, cursor: "pointer", border: "1px solid " + (typeFilter === t ? "#2d4a2d" : "#dde8d8"), background: typeFilter === t ? "#eef5ea" : "#fff", color: "#4a6e4a" }}>
            {t === "all" ? "All types" : DOC_TYPE_LABELS[t]}
          </button>
        ))}
        {groups.length > 0 && (
          <button onClick={() => (groups.every((g) => isGroupOpen(g[0])) ? allClosed() : allOpen())}
            style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, cursor: "pointer", border: "1px solid #dde8d8", background: "#fff", color: "#4a6e4a" }}>
            {groups.every((g) => isGroupOpen(g[0])) ? "▴ Collapse all" : "▾ Expand all"}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: "#60735f", fontSize: 13, padding: 20 }}>Loading documents…</div>
      ) : groups.length === 0 ? (
        <div style={{ color: "#60735f", fontSize: 13, padding: 28, textAlign: "center", border: "1px dashed #dde8d8", borderRadius: 10 }}>
          {docs.length === 0 ? "No documents yet — pull from Dropbox (⚙ Sources) or use ＋ Add files." : `Nothing in “${STATUS_TABS.find((s) => s.key === statusFilter)?.label}”.`}
        </div>
      ) : (
        groups.map(([fid, list]) => (
          <div key={fid} style={{ marginBottom: 10, border: "1px solid #e3ece0", borderRadius: 10, overflow: "hidden" }}>
            <button onClick={() => toggleGroup(fid)}
              style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 8, background: "#f2f6f2", padding: "9px 14px", fontWeight: 800, fontSize: 13, border: "none", cursor: "pointer", color: fid === "__unmatched__" ? "#a02c1a" : "#2d4a2d" }}>
              <span style={{ fontSize: 10, color: "#60735f", width: 10 }}>{isGroupOpen(fid) ? "▾" : "▸"}</span>
              {fid === "__unmatched__" ? "⚠ Unmatched (no farmer)" : (farmersById[fid]?.name || "Unknown farmer")}
              <span style={{ fontWeight: 600, color: "#60735f" }}>· {list.length} doc{list.length === 1 ? "" : "s"}</span>
            </button>
            {isGroupOpen(fid) && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id}>
                    <td style={cellTd}>
                      <div style={{ fontWeight: 700 }}>{d.title || d.original_filename}</div>
                      <div style={{ fontSize: 11, color: "#60735f" }}>
                        {DOC_TYPE_LABELS[d.doc_type] || d.doc_type}{d.crop_year ? ` · CY${d.crop_year}` : ""}{d.policy_number ? ` · ${d.policy_number}` : ""}
                      </div>
                    </td>
                    <td style={{ ...cellTd, whiteSpace: "nowrap" }}>{statusBadge(d)}</td>
                    <td style={{ ...cellTd, textAlign: "right", whiteSpace: "nowrap", width: 1 }}>
                      {menuOpenId === d.id ? (
                        <span style={{ display: "inline-flex", alignItems: "center" }}>
                          {d.storage_path && <button onClick={() => viewPdf(d.storage_path)} style={rowAct("1px solid #c8d8c0", "#fff", "#2d4a2d")}>View</button>}
                          {(d.signature_status === "none" || d.signature_status === "error" || d.signature_status === "draft") && d.storage_path && (
                            <button onClick={() => sendForSignature(d)} style={rowAct("none", "#2d4a2d", "#fff")}>✍️ {d.signature_status === "draft" ? "Resume" : "Set up & send"}</button>
                          )}
                          {d.signature_status === "signed" && d.signed_storage_path && <button onClick={() => viewPdf(d.signed_storage_path)} style={rowAct("1px solid #1f6b1a", "#e3f3e0", "#1f6b1a")}>Signed PDF</button>}
                          {d.doc_type === "acreage_report" && d.storage_path && <AcreageFillButton formDoc={d} farmerName={farmersById[d.farmer_id]?.name || ""} label="🌾 Fill / Check" btnStyle={rowAct("1px solid #1f6b1a", "#eef7ea", "#1f6b1a")} />}
                          {d.signature_status !== "signed" && d.signature_status !== "signed_copy_missing" && <button onClick={() => markSignedManually(d)} title="Signed on paper / iPad" style={rowAct("1px solid #1f6b1a", "#fff", "#1f6b1a")}>✓ Mark signed</button>}
                          <button onClick={() => deleteDoc(d.id)} style={rowAct("1px solid #e6cfc9", "#fff", "#a02c1a")}>Delete</button>
                          <button onClick={() => setMenuOpenId(null)} style={{ ...rowAct("1px solid #dde8d8", "#fff", "#60735f"), fontWeight: 800 }}>✕</button>
                        </span>
                      ) : (
                        <button onClick={() => setMenuOpenId(d.id)} title="Actions" style={{ fontSize: 18, lineHeight: 1, padding: "2px 10px", borderRadius: 6, border: "1px solid #dde8d8", background: "#fff", color: "#4a6e4a", cursor: "pointer" }}>⋯</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FSA 578 MANAGEMENT SCREEN
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PIPELINE BOARD  (the "Workflow" lens — one row per farmer, per season)
// Season tabs (Spring 2026 / Fall 2027 / PRF 2027). Reads farmer_578s +
// farmer_documents (acreage_report) and shows each farmer's stage: 578 →
// 578→Mandy → Acreage Report (AI-fill backup right in its cell) → Signed & Filed.
// Signature status = whether scan-signed found the signed copy in the SIGNED &
// READY Dropbox folder. Fall/PRF carry no docs yet → honest empty states.
// ─────────────────────────────────────────────────────────────────────────────
// ⚠ DERIVED, NOT PINNED. These were three frozen literals — spring2026 /
// fall2027 / prf2027 — and the repo's own triage file already named the disease:
// "Pipeline Board carries the SAME frozen-campaign disease Crop Review had.
// Fixable with the same seasonCropYear rule; not done in this pass."
//
// They happen to be right on 25 September 2026 and go stale by themselves: on
// 1 January the spring tab still says 2026 while spring-2027 578s are what is
// arriving. The campaign rule is shared with api/box.js, so the tab a document
// is filed under and the tab it is displayed under can no longer disagree.
const PB_SEASONS = (() => {
  const { spring, fall } = CAMPAIGN578.liveCampaigns();
  return [
    { key: `spring${spring}`, label: "🌱 Spring", sub: String(spring), season: "spring", year: spring },
    { key: `fall${fall}`, label: "🌾 Fall", sub: String(fall), season: "fall", year: fall },
    // PRF is listed and empty on purpose: `farmer_578s_season_check` only
    // permits 'spring' and 'fall', so a PRF row cannot be written at all. The
    // tab says so rather than implying the documents are merely late.
    { key: `prf${fall}`, label: "🌧️ PRF", sub: String(fall), season: "prf", year: fall },
  ];
})();
// The stat cards double as filters, so each group's rule is defined ONCE here and
// used for both the count and the row filter — a card that says 55 always opens
// exactly those 55. "Farmers" has no rule; it's the clear-filter button.
//
// ⚠⚠ THE CARD ORDER IS THE LADDER ORDER, AND IT WAS NOT. Until 2026-10-01 "Needs
// fill" was drawn THIRD, ahead of "Awaiting signature" and "Signed" — but `canFill`
// requires `inReadyForSilas` (AR_RANK 4), while need_signed is rank 1 and
// signed_pending/signed_ready are 2 and 3. So the row read left-to-right in an order
// no farmer can actually move through, and a reader learning the process from the
// screen learned it backwards. Kevin: *"what is the flow, what is the order of
// operations."*
//
// The order below is AR_RANK's, and "Needs fill" now sits immediately after "Ready
// for Silas" because that is what it is a subset of — `needsFill` is Ready-for-Silas
// minus the ones Silas has already stamped. Three positions apart they read as two
// unrelated queues; adjacent, the relationship is on the screen. `step` is the number
// the card prints, and "Farmers" has none because it is the clear-filter button and
// not a stage anyone is in.
const PB_STATS = [
  { key: "total", label: "Farmers", color: "#1a2e1a", test: () => true },
  { key: "missing578", step: 1, label: "Missing 578", color: "#c0392b", test: (r) => !r.r578 },
  { key: "awaiting", step: 2, label: "Awaiting signature", color: "#8a6a00", test: (r) => r.arStage === "need_signed" },
  { key: "signed", step: 3, label: "Signed", color: "#1a4a7a", test: (r) => !r.isDone && (r.arStage === "signed_pending" || r.arStage === "signed_ready") },
  // Reads the live folder contents, not the collapsed stage — a farmer who also has
  // an older keyed report still shows here while a signed report is genuinely sitting
  // in Ready for Silas waiting to be filled. But NOT one already in Silas Filled: once
  // filed, a leftover signed copy is stale, so Silas Filled wins and he drops out of
  // this queue (re-fill for a revision is the per-row Re-fill button, not this card).
  { key: "readyForSilas", step: 4, label: "Ready for Silas", color: "#1f6b1a", test: (r) => r.inReadyForSilas && !r.inSilasFilled },
  // A SUBSET of the card above, not a later stage — hence 4a. Same rank in the ladder;
  // what differs is whether Silas has stamped it yet.
  { key: "needsFill", step: "4a", label: "Needs fill", color: "#8a6a00", test: (r) => r.canFill && !r.filledAt },
  // ONE "filled" card (was two: "AI-filled" + "Silas Filled", which Kevin read as the
  // same thing and largely are). They were subtly different — filled_at means Silas
  // stamped it, arStage=silas_filled means the stamped copy has been FILED into the
  // Dropbox outbox — so this counts either, i.e. "Silas has filled it and Mandy hasn't
  // keyed it yet". The filed-vs-not-yet-filed distinction survives on the row chip
  // ("filled · needs filing" vs "filed · needs keying"), where it's actionable, instead
  // of as two headline numbers that drift apart by one and invite the question.
  // Counts a farmer whose filled copy is physically in Silas Filled (folder truth,
  // via inSilasFilled) OR who's filled-not-yet-keyed by the DB — so a report sitting
  // in Silas Filled is never masked by an older keyed copy of the same farmer.
  { key: "silasFilled", step: 5, label: "Silas Filled", color: "#1a5a2a", test: (r) => r.inSilasFilled || (!r.isDone && (!!r.filledAt || r.arStage === "silas_filled")) },
  { key: "done", step: 6, label: "Done (keyed)", color: "#2d6a1a", test: (r) => r.isDone },
];
// ── PRF pipeline board ───────────────────────────────────────────────────────
// The PRF tab of File Management. Same engine as the Coverage Advisor's PRF
// Renewals list (src/prf/prfRenewals.js) — this one is stage-shaped, because
// File Management's question is "what is sitting where", while the Renewals
// list's question is "who still needs a call".
//
// ⚠ IT IS A BOARD OVER THE BOOK, NOT OVER THE FOLDER. Rows are the customers
// who carry PRF; the folder decides each row's STAGE. A document-first board
// would list only the packets that exist, which is precisely the list that
// cannot show you the farmer nobody has started.
//
// PRF's folder is not the `<YEAR> <SEASON> <TYPE>` formula the spring and fall
// boards rely on — it is discovered per year (see discoverPrfRoots in
// api/_dropbox.js), and there is no folder at all until somebody makes one.
function PRFPipelineBoard({ year }) {
  const [pols, setPols] = useState([]);
  const [prfPols, setPrfPols] = useState([]);
  const [ivs, setIvs] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanErr, setScanErr] = useState(null);
  const [scanning, setScanning] = useState(true);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);       // a stage key, or "__none" / "__written"
  const isMobile = useIsMobile();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const [p, pp, iv, f] = await Promise.all([
          sbFetch("policies", { select: "id,farmer_id,policy_number,crop_year,agent_name,agent_name_override,state,aip,policy_type", filters: ["policy_type=eq.prf"] }),
          sbFetch("prf_policies", { select: "id,policy_id,policy_number,county,coverage_level,total_acres,total_liability,total_premium" }),
          sbFetch("prf_intervals", { select: "prf_policy_id,county,grid_id,interval_code,insured_acres" }),
          sbFetch("farmers", { select: "id,name" }),
        ]);
        if (!alive) return;
        const nm = {}; (f || []).forEach(r => { if (r.id) nm[r.id] = r.name; });
        setPols(p || []); setPrfPols(pp || []); setIvs(iv || []); setNames(nm);
      } catch (e) { if (alive) setErr(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setScanning(true); setScanErr(null);
      try {
        const r = await fetch(`/api/market?fn=dropbox&action=stage-scan&season=PRF&year=${year}&summary=1`,
          { method: "POST", headers: getApiAuthHeaders() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        // ⚠ A 200 IS NOT AN ANSWER. A response that parses but carries no `docs`
        // — a proxy, a rewrite, an endpoint that moved — would leave every row
        // reading "nothing filed" with no absence notice, which is the
        // no-op-that-returns-200 failure this repo already records. Refuse the
        // shape rather than render it as a fact about the customers.
        if (!d || !d.docs) throw new Error("the scan returned no document data");
        if (alive) setScan(d);
      } catch (e) { if (alive) { setScanErr(e.message); setScan(null); } }
      finally { if (alive) setScanning(false); }
    })();
    return () => { alive = false; };
  }, [year]);

  const enginePolicies = useMemo(() => {
    const polById = {}; (pols || []).forEach(p => { polById[p.id] = p; });
    const ivBy = {}; (ivs || []).forEach(r => { (ivBy[r.prf_policy_id] = ivBy[r.prf_policy_id] || []).push(r); });
    const out = [];
    for (const pp of prfPols || []) {
      const parent = polById[pp.policy_id];
      if (!parent || !parent.farmer_id) continue;
      out.push({
        farmerId: parent.farmer_id, cropYear: parent.crop_year, aip: parent.aip || null,
        state: parent.state || null, policyNumber: pp.policy_number || parent.policy_number,
        county: pp.county || null, coverageLevel: pp.coverage_level,
        totalAcres: pp.total_acres, totalLiability: pp.total_liability, totalPremium: pp.total_premium,
        intervals: (ivBy[pp.id] || []).map(r => ({ gridId: r.grid_id, county: r.county, intervalCode: r.interval_code, insuredAcres: r.insured_acres })),
      });
    }
    return out;
  }, [pols, prfPols, ivs]);

  const built = useMemo(
    () => PRF_RENEW.buildPrfRenewalRows({ prfPolicies: enginePolicies, namesById: names, year, stages: scan && scan.docs }),
    [enginePolicies, names, year, scan]);
  const rows = built.rows;
  const unmatched = useMemo(() => PRF_RENEW.prfUnmatchedDocCores({ stages: scan && scan.docs, rows }), [scan, rows]);

  // The ladder as it is ACTUALLY spelled in this year's folder, most advanced
  // first, plus the two states no folder can hold: nobody has started, and the
  // policy is already written. Built from the scan's own discovered ladder so a
  // stage that does not exist in this year's folder is not offered as a box
  // that can never fill.
  const stageKeys = useMemo(() => {
    const inUse = new Set(rows.map(r => r.docStage).filter(Boolean));
    const order = ["keyed", "no_changes", "signed_ready", "signed_pending", "pre_prints", "need_signed", "blank"];
    return order.filter(s => inUse.has(s));
  }, [rows]);

  const counts = useMemo(() => {
    const c = { __none: 0, __written: 0 };
    for (const r of rows) {
      if (!r.docStage) c.__none++;
      else c[r.docStage] = (c[r.docStage] || 0) + 1;
      if (r.renewed) c.__written++;
    }
    return c;
  }, [rows]);

  const view = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let v = rows;
    if (pick === "__none") v = v.filter(r => !r.docStage);
    else if (pick === "__written") v = v.filter(r => r.renewed);
    else if (pick) v = v.filter(r => r.docStage === pick);
    if (needle) v = v.filter(r => r.name.toLowerCase().includes(needle) || r.counties.some(c => c.toLowerCase().includes(needle)));
    return v;
  }, [rows, pick, q]);

  const box = (key, label, n, color) => {
    const on = pick === key;
    return (
      <button key={key} onClick={() => setPick(on ? null : (n ? key : pick))} disabled={!n && !on}
        style={{ background: on ? "#eef6ec" : "#fff", border: `1px solid ${on ? "#1f6b1a" : "#dde8d8"}`,
                 boxShadow: on ? "0 0 0 2px rgba(31,107,26,.16)" : "none", borderRadius: 10, padding: "10px 15px",
                 minWidth: 108, textAlign: "left", cursor: n || on ? "pointer" : "default", opacity: !n && !on ? 0.5 : 1, font: "inherit" }}>
        <div style={{ fontSize: 21, fontWeight: 800, color }}>{n}</div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#4a6e4a" }}>{label}</div>
      </button>
    );
  };
  const docMissing = !!(scan && scan.docs && scan.docs.prf && scan.docs.prf.root_missing);
  const soiMissing = !!(scan && scan.docs && scan.docs.prf_soi && scan.docs.prf_soi.root_missing);
  const unknownFolders = (scan && scan.docs && scan.docs.prf && scan.docs.prf.unknown_folders) || [];
  const roots = (scan && scan.prf_roots) || null;

  if (loading) return <div style={{ textAlign: "center", padding: "60px 0", color: "#60735f", fontSize: 15 }}>Loading the PRF book…</div>;
  if (err) return <div style={{ padding: 14, color: "#a5461b", background: "#fbeee6", border: "1px solid #eccdba", borderRadius: 10, fontSize: 13 }}>Couldn’t load the PRF book: {err}</div>;

  return (
    <div>
      <div style={{ fontSize: 12.5, color: "#3a4a38", background: "#f2f6f2", border: "1px solid #dde8d8", borderRadius: 9, padding: "9px 12px", marginBottom: 12, lineHeight: 1.6 }}>
        PRF runs <b>one</b> document — the renewal / acreage-reporting packet — through the same stage folders as an app renewal, then the SOI comes back.
        These are the <b>{built.baseYear}</b> PRF customers being renewed for <b>{built.renewalYear}</b>.
        {roots && roots.doc ? <span style={{ color: "#60735f" }}> · reading <code style={{ fontSize: 11 }}>{roots.doc}</code></span> : null}
      </div>

      <div style={{ marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search farmer or county…"
          style={{ width: "100%", maxWidth: 360, padding: "9px 14px", border: "1px solid #c8d8c0", borderRadius: 9, fontSize: 13, boxSizing: "border-box" }} />
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
        {box("__all", "PRF customers", rows.length, "#1a2e1a")}
        {box("__none", "Nothing filed", counts.__none || 0, "#c0392b")}
        {stageKeys.map(s => box(s, (RENEWAL_STAGE[s] && RENEWAL_STAGE[s].lbl) || s, counts[s] || 0,
          RENEWAL_DONE.has(s) ? "#2d6a1a" : "#8a6a00"))}
        {box("__written", `Written for ${built.renewalYear}`, counts.__written || 0, "#1a4a7a")}
        {pick && <button onClick={() => setPick(null)} style={{ border: "none", background: "none", color: "#1f6b1a", fontWeight: 700, fontSize: 11.5, cursor: "pointer", textDecoration: "underline" }}>clear filter</button>}
      </div>

      {(scanErr || docMissing || soiMissing || unknownFolders.length || unmatched.length) ? (
        <div style={{ fontSize: 11.5, color: "#7a6a4a", background: "#f9f6ef", border: "1px solid #e6ddc9", borderRadius: 9, padding: "9px 12px", marginBottom: 12, lineHeight: 1.7 }}>
          <b style={{ color: "#5c5030" }}>What this board could not read</b>
          {scanErr && <div>· Dropbox scan failed ({scanErr}) — every row's stage is blank because of OUR gap, not theirs.</div>}
          {docMissing && <div>· No <b>{year} PRF</b> folder exists yet{roots && roots.searched ? ` (looked in ${roots.searched.join(" and ")})` : ""}. Nothing has been prepared for this renewal — the board fills in on its own once the folder is made.</div>}
          {soiMissing && !docMissing && <div>· No <b>{year} PRF SOI</b> folder yet — schedules come back after keying.</div>}
          {unknownFolders.length > 0 && <div>· Not a recognised stage folder, so its files are NOT ranked: {unknownFolders.slice(0, 4).join(" · ")}</div>}
          {unmatched.length > 0 && <div>· {unmatched.length} document{unmatched.length === 1 ? "" : "s"} name a policy number nobody here holds — {unmatched.slice(0, 6).join(" · ")}</div>}
        </div>
      ) : null}

      {view.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 12, padding: "40px 24px", textAlign: "center", color: "#60735f" }}>
          {rows.length ? "Nothing in this group." : `No PRF policies in ${built.baseYear} or ${built.renewalYear}.`}
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, overflowX: "auto" }}>
          {!isMobile && (
            <div style={{ display: "grid", gridTemplateColumns: "1.7fr 1.1fr .7fr 1.4fr .8fr .9fr", gap: 8, background: "#eef4ea", borderBottom: "1px solid #c8d8c0", padding: "9px 16px", fontSize: 10, fontWeight: 800, color: "#4a6e4a", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              <div>Farmer</div><div>County</div><div>Acres</div><div>Renewal / AR packet</div><div>SOI</div><div>{built.renewalYear} policy</div>
            </div>
          )}
          {view.map((r, i) => {
            const meta = r.docStage ? RENEWAL_STAGE[r.docStage] : null;
            const t = RENEWAL_TONE[meta ? meta.tone : "none"] || RENEWAL_TONE.none;
            return (
              <div key={r.fid} style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: "1.7fr 1.1fr .7fr 1.4fr .8fr .9fr", gap: 8,
                                        padding: "10px 16px", borderBottom: i === view.length - 1 ? "none" : "1px solid #eef2ec", fontSize: 12.5, alignItems: "center" }}>
                <div style={{ fontWeight: 700, color: "#1a2e1a" }}>{r.name}
                  {r.aips.length ? <span style={{ fontSize: 10, color: "#677167", fontWeight: 600 }}> · {r.aips.map(a => a === "RAIN_AND_HAIL" ? "R&H" : a).join(", ")}</span> : null}
                </div>
                <div style={{ color: "#5c6f5c" }}>{r.counties.map(c => c.charAt(0) + c.slice(1).toLowerCase()).join(", ") || "—"}</div>
                <div style={{ fontVariantNumeric: "tabular-nums", color: "#3a4a38" }}>{r.base && r.base.acres != null ? fmt(r.base.acres) : "—"}</div>
                <div><span title={meta ? meta.txt : "the scan found no document for this customer"} style={{ ...cellPill(t) }}>{t.mark} {meta ? meta.lbl : "nothing filed"}</span></div>
                <div>{r.soiOnFile ? <span style={{ ...cellPill(RENEWAL_TONE.done) }}>✓ on file</span> : <span style={{ ...cellPill(RENEWAL_TONE.none) }}>—</span>}</div>
                {/* Same three answers as the roster — see prfRenewalIsElected. A rule with a
                    third state and a page with two is the defect unfixed, and this board was
                    the SECOND surface painting the shells as written. */}
                <div>{r.renewed
                  ? <span style={{ ...cellPill(RENEWAL_TONE.done) }}>✓ written</span>
                  : r.renewalShell
                    ? <span style={{ ...cellPill(RENEWAL_TONE.todo) }} title="The carrier has opened a policy record for this crop year — PRF carries over unless cancelled — but nothing is elected on it: no coverage level, no acres, no premium, no intervals.">◦ record opened</span>
                    : <span style={{ ...cellPill(RENEWAL_TONE.todo) }}>• not yet</span>}</div>
              </div>
            );
          })}
        </div>
      )}
      {scanning && <div style={{ fontSize: 11, color: "#677167", marginTop: 8 }}>Reading the Dropbox PRF folder…</div>}
    </div>
  );
}

function PipelineBoard({ farmers, readOnly = false }) {
  const [f578, setF578] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  // ⚠ A PERSISTED TAB IS UNTRUSTED INPUT (see PRFAdvisorView) — validate against the
  // seasons this build actually has, or a renamed key renders the tab row over nothing
  // while every static check reports the page clean.
  //
  // ⚠⚠ AND THE FALLBACK IS DERIVED, NOT THE LITERAL "spring2026". Both sides of the
  // rebase that produced this line were half right: one added the persistence and fell
  // back to a pinned key, the other derived the default and had no persistence. A pinned
  // "spring2026" points at a key PB_SEASONS no longer contains the moment the campaigns
  // roll, and the board then opens on nothing — which is the frozen-campaign disease this
  // file records for Crop Review and the Pipeline Board already. Persist, validate, and
  // fall back to the first LIVE campaign.
  const [seasonKey, setSeasonKeyRaw] = useState(() => {
    try {
      const s = sessionStorage.getItem("cloverag_pipeline_season");
      return PB_SEASONS.some(x => x.key === s) ? s : PB_SEASONS[0].key;
    } catch { return PB_SEASONS[0].key; }
  });
  const setSeasonKey = (k) => { try { sessionStorage.setItem("cloverag_pipeline_season", k); } catch {} setSeasonKeyRaw(k); };
  const [rescanMsg, setRescanMsg] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const [busyFwd, setBusyFwd] = useState({});
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ col: "name", dir: "asc" });
  const [stageMap, setStageMap] = useState({}); // policyCore → current Dropbox folder stage
  const [stageErr, setStageErr] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [stageFiles, setStageFiles] = useState([]);
  // Folders the stage scan could not list (stage-scan `listing_errors`). A farm
  // whose stage ranks below one of these reads "stage unknown", never lower.
  const [stageListingErrors, setStageListingErrors] = useState([]);
  const [xtraFarmers, setXtraFarmers] = useState([]); // book-wide farmers self-loaded when the prop is empty (portal viewer)
  const [statFilter, setStatFilter] = useState(null); // PB_STATS key — click a card to drill in
  const [sel, setSel] = useState(() => new Set()); // farmer_ids checked for batch AI-fill
  const [batch, setBatch] = useState(null); // { phase, total, done, results[], log[], choices }
  const [choiceSel, setChoiceSel] = useState({}); // fid → { crop: "reported"|"grain" } for mixed-use 578s
  const [sendingAll, setSendingAll] = useState(false);
  const [pushPicker, setPushPicker] = useState(null); // { targets } — Dropbox destination browser
  const [pushing, setPushing] = useState(false);
  const isMobile = useIsMobile();

  // AR workflow stage = which Dropbox folder the form sits in. "blank" = loose in
  // the MAIN acreage folder (needs keying → AI-fill); the rest are stage subfolders.
  //
  // e-sign changed the real-world order (2026-07-17, per Kevin): reports now come
  // back SIGNED before anyone keys them, and they land in SIGNED & READY. So
  // signed_ready no longer implies keyed — it means signed, still awaiting keying,
  // same as signed_pending. Only KEYED is terminal. The fill gate's axis has always
  // been "has this been keyed yet?", not "has this been signed?" — which is why
  // signed_pending was already fillable and signed_ready now is too.
  const AR_RANK = { blank: 0, need_signed: 1, signed_pending: 2, signed_ready: 3, ready_for_silas: 4, silas_filled: 5, keyed: 6 };
  const pcore = (s) => { const runs = String(s || "").match(/\d{6,}/g); return runs && runs.length ? runs.sort((a, b) => b.length - a.length)[0] : null; };

  async function load() {
    setLoading(true); setStageErr(null);
    try {
      const [a, b, p] = await Promise.all([
        sbFetch("farmer_578s"),
        sbFetch("farmer_documents", { filters: ["doc_type=eq.acreage_report"] }),
        sbFetch("policies", { select: "farmer_id,policy_number" }),
      ]);
      setF578(a || []); setDocs(b || []); setPolicies(p || []);
      if (!(farmers || []).length) { try { setXtraFarmers(await sbFetch("farmers", { select: "id,name" }) || []); } catch (e2) {} }
    } catch (e) { console.error("Pipeline load failed:", e); }
    setLoading(false);
    // Live Dropbox stage scan runs after the table renders so the board isn't
    // blocked on it; the stage columns fill in when it returns.
    try {
      const r = await fetch(`/api/market?fn=dropbox&action=stage-scan`, { method: "POST", headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setStageMap(d.stages || {});
      setStageFiles(d.files || []);
      setStageListingErrors(Array.isArray(d.listing_errors) ? d.listing_errors : []);
    } catch (e) { setStageErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  const farmerMap = useMemo(() => { const m = {}; [...(farmers || []), ...xtraFarmers].forEach(f => { if (f.id) m[f.id] = f; }); return m; }, [farmers, xtraFarmers]);
  const cur = PB_SEASONS.find(s => s.key === seasonKey);

  const rows = useMemo(() => {
    if (cur.season === "prf") return [];
    const s578 = (f578 || []).filter(r => r.season === cur.season && r.tax_year === cur.year && r.farmer_id);
    const yDocs = (docs || []).filter(d => d.crop_year === cur.year && d.farmer_id);
    const ids = new Set([...s578.map(r => r.farmer_id), ...yDocs.map(d => d.farmer_id)]);
    // farmer → policy cores (from the policies table), so the AR's Dropbox stage is
    // known even when nothing is indexed for that farmer (e.g. Kirby, already KEYED).
    const coresByFarmer = {};
    (policies || []).forEach(p => { const c = pcore(p.policy_number); if (p.farmer_id && c) (coresByFarmer[p.farmer_id] = coresByFarmer[p.farmer_id] || []).push(c); });
    // Which farmers have a file sitting in each stage folder RIGHT NOW.
    //
    // Derived from the scanned file list, NOT from arStage: arStage collapses to a
    // single "most advanced" value per farmer, so a LATER stage hides a file still
    // waiting in an EARLIER folder. Both real cases, 2026-07-20 — Lismore resolved
    // to silas_filled (filled copy outranking the signed original still queued) and
    // Farabaugh to keyed (an older report in KEYED), so both dropped out of the
    // Ready-for-Silas queue and out of canFill while genuinely needing a fill.
    //
    // Each file resolves to AT MOST ONE farmer: every name token must appear, else a
    // single unambiguous long-token match (covers "Farabaugh_2026…" for RICHARD
    // FARABAUGH, where the first name never appears in the filename). A file that
    // matches two farmers is skipped rather than guessed onto one of them.
    const foldersByFarmer = {};
    // Same attribution, but keep each file's NAME so a multi-state farmer's folders can
    // be resolved PER STATE (a file's state shows in its policy prefix: "…PA-942-…").
    const filesByFarmer = {};
    if (stageFiles.length) {
      const STOP = ["INC", "LLC", "LLP", "THE", "AND", "DBA", "FARM", "FARMS", "FARMING"];
      const tokOf = (nm) => String(nm || "").toUpperCase().replace(/[^A-Z0-9 ]/g, " ").split(/\s+/).filter(t => t.length > 2 && !STOP.includes(t));
      // Match a name token as a WHOLE WORD, not a raw substring. "HAR-GO FARMS LLC"
      // reduces to the single token "HAR", and "HAR" sits inside "C-HAR-DON" — so
      // substring matching made HAR-GO match CHARDON's file, both tied at one token,
      // and Chardon was dropped as ambiguous (invisible in Ready for Silas, 2026-07-21).
      // Splitting on non-alphanumerics keeps "HAR" matching "HAR-GO" itself.
      const fileToks = (s) => new Set(String(s || "").toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean));
      // EXACT doc-path attribution first: a farmer's own indexed docs record the exact
      // Dropbox filename they were sourced/filed as, so a stage file that matches one
      // of those names belongs to that farmer with certainty — even when the name is
      // ambiguous across duplicate farmer records. This is what lets Yahner's filled
      // copy attribute to the LLC record (which holds the filled doc) instead of being
      // skipped as ambiguous across the three Yahner rows (2026-07-24).
      const fileToFarmer = {};
      yDocs.forEach(d => {
        for (const p of [d.dropbox_path, d.filled_dropbox_path, d.signed_storage_path]) {
          if (!p) continue;
          const base = String(p).split("/").pop().toUpperCase();
          if (base && !fileToFarmer[base]) fileToFarmer[base] = d.farmer_id;
        }
      });
      // ids is a Set — spread before mapping (Set has forEach but no map).
      const cands = [...ids].map(fid => ({ fid, toks: tokOf(farmerMap[fid]?.name) })).filter(c => c.toks.length);
      stageFiles.forEach(f => {
        const exact = fileToFarmer[String(f.n).toUpperCase()];
        if (exact) { (foldersByFarmer[exact] = foldersByFarmer[exact] || new Set()).add(f.s); (filesByFarmer[exact] = filesByFarmer[exact] || []).push({ n: f.n, s: f.s }); return; }
        const ft = fileToks(f.n);
        let hit = cands.filter(c => c.toks.every(t => ft.has(t)));
        // Prefer the most specific name when several match. "Lismore_Dairy_2026…"
        // matches LISMORE DAIRY (2 tokens) and also D&D DAIRY, whose only token
        // over two characters is the generic "DAIRY" — so it would otherwise match
        // every file with "Dairy" in the name. Most tokens matched wins.
        if (hit.length > 1) {
          const best = Math.max(...hit.map(c => c.toks.length));
          hit = hit.filter(c => c.toks.length === best);
        }
        if (!hit.length) hit = cands.filter(c => c.toks.some(t => t.length >= 6 && ft.has(t)));
        if (hit.length !== 1) return; // still ambiguous — don't guess
        if (!foldersByFarmer[hit[0].fid]) foldersByFarmer[hit[0].fid] = new Set();
        foldersByFarmer[hit[0].fid].add(f.s);
        (filesByFarmer[hit[0].fid] = filesByFarmer[hit[0].fid] || []).push({ n: f.n, s: f.s });
      });
    }

    // A field's/report's STATE is its policy prefix (MD-942-…, PA-942-…, NY-944-…).
    const stateOfPolicy = (pn) => { const m = String(pn || "").match(/^([A-Z]{2})-/); return m ? m[1] : ""; };
    // Build one board line. `state` null = the whole farmer (single-state, unchanged);
    // a 2-letter state = ONE state's acreage report for a grower who farms in two
    // states (Archer/Stewart/Schofield/Susquehanna), tracked/filled/filed on its own.
    // `lineArs` are the docs for this line (all of them, or one state's).
    const buildRow = (fid, state, lineArs) => {
      const r578 = s578.find(r => r.farmer_id === fid) || null;
      const ars = lineArs;
      // signed_copy_missing: the grower DID sign (we only lack the PDF), so the stage still advances.
      const signedByStatus = ars.some(d => d.signature_status === "signed" || d.signature_status === "signed_copy_missing");
      // AR stage from the live Dropbox folder scan, matched by policy cores. When split
      // by state, use ONLY this state's cores (its policies + this line's docs) — a
      // policy core is state-specific, so the stage is too.
      const cores = new Set();
      (policies || []).forEach(p => { if (p.farmer_id !== fid) return; if (state && stateOfPolicy(p.policy_number) !== state) return; const c = pcore(p.policy_number); if (c) cores.add(c); });
      ars.forEach(d => { const c = pcore(d.policy_number); if (c) cores.add(c); });
      let arStage = null;
      cores.forEach(c => { const st = stageMap[c]; if (st && (arStage === null || AR_RANK[st] > AR_RANK[arStage])) arStage = st; });
      // Name-token fallback (unnamed-by-policy R&H scans) — single-state only: the two
      // states share the farmer name, so a name match can't tell them apart.
      if (arStage === null && !state && stageFiles.length) {
        const toks = (farmerMap[fid]?.name || "").toUpperCase().replace(/[^A-Z0-9 ]/g, " ").split(/\s+/).filter(t => t.length > 2 && !["INC", "LLC", "LLP", "THE", "AND", "DBA", "FARM", "FARMS", "FARMING"].includes(t));
        if (toks.length) stageFiles.forEach(f => { const up = (f.n || "").toUpperCase(); if (toks.every(t => up.includes(t)) && (arStage === null || AR_RANK[f.s] > AR_RANK[arStage])) arStage = f.s; });
      }
      // Which folders this line has a LIVE file in right now (folder truth, not the
      // collapsed stage). Per state, resolve via the state prefix in the filename
      // ("…PA-942-…"); else the farmer aggregate. Both override "most-advanced-wins":
      // a signed report in Ready for Silas is fillable, a filled copy in Silas Filled
      // needs keying — each stays visible even when an older copy sits in KEYED.
      let inReadyForSilas, inSilasFilled;
      if (state) {
        const re = new RegExp(`\\b${state}-\\d`);
        const sf = (filesByFarmer[fid] || []).filter(f => re.test(String(f.n).toUpperCase()));
        inReadyForSilas = sf.some(f => f.s === "ready_for_silas");
        inSilasFilled = sf.some(f => f.s === "silas_filled");
      } else {
        inReadyForSilas = !!(foldersByFarmer[fid] && foldersByFarmer[fid].has("ready_for_silas"));
        inSilasFilled = !!(foldersByFarmer[fid] && foldersByFarmer[fid].has("silas_filled"));
      }
      // KEYED = done, UNLESS a newer report is still upstream (Yahner 2026-07-24).
      const isDone = arStage === "keyed" && !inSilasFilled && !inReadyForSilas;
      const stageUnknown = stageIsBlind(arStage, stageScanBlindRank({ listing_errors: stageListingErrors }, AR_RANK), AR_RANK);
      const isSigned = signedByStatus || arStage === "signed_ready" || arStage === "signed_pending" || isDone;
      // Most recent AI-fill on THIS line's docs (filled_at, written by action=fill).
      const filledAt = ars.reduce((m, d) => (d.filled_at && (!m || d.filled_at > m) ? d.filled_at : m), null);
      const filledDoc = filledAt ? ars.find(d => d.filled_at === filledAt) || null : null;
      const filledPath = filledDoc ? filledDoc.filled_storage_path || null : null;
      const filledDocId = filledDoc ? filledDoc.id : null;
      const pushedAt = filledDoc ? filledDoc.pushed_to_dropbox_at || null : null;
      const pushedPath = filledDoc ? filledDoc.filled_dropbox_path || null : null;
      // A per-state (split) row can't detect its Silas-Filled copy by filename — the
      // filed PDF is "<farmer>_Acreage_Report_FILLED.pdf" with NO policy number, so the
      // state-prefix match misses it and the row would show no Re-fill button (Stewart
      // PA, 2026-08-05). Use the DB instead: pushed_to_dropbox_at on THIS state's form
      // means it was filed. Non-split rows keep the folder signal (the farmer's filled
      // copy attributes by name fine).
      const filed = inSilasFilled || (!!state && !!pushedAt);
      // Fillable = has a 578, not keyed, not out for signature, not already filed to
      // Silas Filled (waiting on Mandy). Silas Filled wins over Ready for Silas once
      // filed, so readyToFill excludes it; re-filling a revision is canRefill.
      const readyToFill = inReadyForSilas && !filed;
      const canFill = !!r578 && !isDone && (stageFiles.length
        ? readyToFill
        : (arStage !== "need_signed" && arStage !== "silas_filled"));
      const canRefill = !!r578 && !isDone && !canFill && filed;
      // A per-state (split) AR can ALWAYS be re-run from its stored form — stage-fillable's
      // state-scoped fallback finds it without anything staged in Ready for Silas. This is
      // the escape hatch for a two-state farmer that reads keyed or filed-but-missing
      // (Brian Sump: OH keyed manually + never AI-filled, PA filed but its Dropbox copy
      // gone, 2026-08-06). Non-split rows keep the stricter canFill/canRefill gating.
      const canRerun = !!state && !!r578 && !canFill && !canRefill;
      return { fid, key: state ? `${fid}|${state}` : fid, state: state || null, name: farmerMap[fid]?.name || r578?.extracted_name || "Unknown", county: state ? null : (r578?.county || null), r578, ars, arCount: ars.length, arStage, stageUnknown, inReadyForSilas, inSilasFilled: filed, isSigned, isDone, filledAt, filledDocId, filledPath, pushedAt, pushedPath, canFill, canRefill, canRerun };
    };

    const out = [];
    ids.forEach(fid => {
      const allArs = yDocs.filter(d => d.farmer_id === fid);
      // States this farmer has an acreage form for — from indexed docs AND from the
      // Dropbox staged files. A form can sit in Ready for Silas WITHOUT an indexed doc
      // row: Schofield's NY form was in Dropbox but only his PA was indexed, so the
      // split saw one state and collapsed him to a single (wrong-labeled) row. The stage
      // scan sees both, so read the state off the staged filenames too.
      const stateSet = new Set(allArs.map(d => stateOfPolicy(d.policy_number)).filter(Boolean));
      (filesByFarmer[fid] || []).forEach(f => { const m = String(f.n || "").toUpperCase().match(/\b([A-Z]{2})-\d{3}-\d/); if (m) stateSet.add(m[1]); });
      const states = [...stateSet];
      if (states.length > 1) {
        states.sort().forEach(state => out.push(buildRow(fid, state, allArs.filter(d => stateOfPolicy(d.policy_number) === state))));
      } else {
        out.push(buildRow(fid, null, allArs));
      }
    });
    return out.sort((a, b) => a.name.localeCompare(b.name) || String(a.state || "").localeCompare(String(b.state || "")));
  }, [f578, docs, cur, farmerMap, stageMap, policies, stageFiles, stageListingErrors]);

  // Counts come from the same PB_STATS rules the filter uses.
  const stats = useMemo(() => {
    const o = {};
    PB_STATS.forEach(s => { o[s.key] = rows.filter(s.test).length; });
    return o;
  }, [rows]);

  // search + sortable columns over the season's rows. Status columns sort by a
  // simple rank (missing/none = low, done = high) so ascending brings the
  // to-do items to the top; click again to flip.
  const sortVal = (r, col) => {
    switch (col) {
      case "p578": return r.r578 ? 1 : 0;
      case "mandy": return !r.r578 ? -1 : r.r578.forwarded_to_mandy ? 1 : 0;
      case "ar": return r.arStage ? AR_RANK[r.arStage] : (r.arCount === 0 ? -2 : -1);
      case "signed": return r.isDone ? 5 : r.arStage === "silas_filled" ? 4 : r.arStage === "ready_for_silas" ? 3 : (r.arStage === "signed_pending" || r.arStage === "signed_ready") ? 2 : r.arStage === "need_signed" ? 1 : 0;
      default: return 0;
    }
  };
  const view = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let v = needle ? rows.filter(r => `${r.name} ${r.county || ""}`.toLowerCase().includes(needle)) : rows.slice();
    // Stat-card drill-in, applied alongside the search rather than instead of it —
    // "AI-filled" + a name search narrows to that farmer within the group.
    const def = statFilter && PB_STATS.find(s => s.key === statFilter);
    if (def) v = v.filter(def.test);
    const { col, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;
    if (col === "name") v.sort((a, b) => mult * a.name.localeCompare(b.name));
    else v.sort((a, b) => { const d = sortVal(a, col) - sortVal(b, col); return d !== 0 ? mult * d : a.name.localeCompare(b.name); });
    return v;
  }, [rows, q, sort, statFilter]);
  const toggleSort = (col) => setSort(p => p.col === col ? { col, dir: p.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" });
  const arrow = (col) => sort.col === col ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  // Batch selection is only ever offered over *eligible* rows — no 578, already
  // keyed, or out for signature can't be checked at all. Select-all targets the
  // farmers still needing a fill; an already-filled farmer can still be ticked
  // individually to deliberately re-fill him.
  // Selection is keyed by farmer_id over ALL rows, so it survives the search box:
  // check a farmer, search for another, check him too → the batch runs both. Deriving
  // the batch target from `view` (search-filtered) instead silently dropped every
  // pick that fell out of the current query — the count looked like it reset, and a
  // run would have quietly filled only whoever matched the search at that moment.
  const eligibleView = useMemo(() => view.filter(r => r.canFill), [view]); // what's on screen now
  const needing = useMemo(() => eligibleView.filter(r => !r.filledAt), [eligibleView]);
  // Selection is keyed by row.key (fid, or fid|state for a split two-state line) so a
  // two-state farmer's MD and PA lines select independently. Batch excludes split rows
  // (they're filled per state), so only single-state rows ever enter `sel`.
  const selRows = useMemo(() => rows.filter(r => r.canFill && sel.has(r.key)), [rows, sel]);
  const selHidden = useMemo(() => selRows.filter(r => !view.some(v => v.key === r.key)).length, [selRows, view]);
  // Selected farmers with a stamped PDF that hasn't been filed yet.
  const selPushable = useMemo(() => selRows.filter(r => r.filledPath && !r.pushedAt), [selRows]);
  const allSel = needing.length > 0 && needing.every(r => sel.has(r.key));
  const toggleSel = (key) => setSel(p => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  // Select-all covers the CURRENT search only, so it merges in/out rather than
  // replacing the set — otherwise it wipes picks made under a different query.
  const toggleAll = () => setSel(p => {
    const n = new Set(p);
    needing.forEach(r => { if (allSel) n.delete(r.key); else n.add(r.key); });
    return n;
  });
  useEffect(() => { setSel(new Set()); setStatFilter(null); }, [seasonKey]); // a season switch is a different work list

  async function forwardMandy(row) {
    if (!row.r578) return;
    setBusyFwd(p => ({ ...p, [row.r578.id]: true }));
    try {
      const r = await fetch(`/api/box?action=email-forward&id=${row.r578.id}`, { method: "POST", headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setF578(prev => prev.map(x => x.id === row.r578.id ? { ...x, forwarded_to_mandy: true, forwarded_at: new Date().toISOString() } : x));
    } catch (e) { alert("Forward to Mandy failed: " + e.message); }
    setBusyFwd(p => ({ ...p, [row.r578.id]: false }));
  }
  // Open the tab SYNCHRONOUSLY, inside the click, then point it at the signed URL
  // once that resolves. Calling window.open() after an await is no longer part of
  // the user gesture, so browsers block it as a popup and the click appears to do
  // nothing at all — no error, no tab.
  async function viewDoc(path) {
    const w = window.open("", "_blank");
    try {
      const url = await sbStorageSignedUrl("farmer-documents", path);
      if (w) w.location.href = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); alert("Couldn't open: " + e.message); }
  }
  // Open the copy that was FILED into Dropbox (what Mandy keys from), via a
  // short-lived Dropbox temporary link. Same synchronous-open trick.
  async function viewDropbox(path) {
    const w = window.open("", "_blank");
    try {
      const r = await fetch(`/api/market?fn=dropbox&action=temp-link&path=${encodeURIComponent(path)}`, { method: "POST", headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.link) throw new Error(d.error || `HTTP ${r.status}`);
      if (w) w.location.href = d.link; else window.open(d.link, "_blank");
    } catch (e) { if (w) w.close(); alert("Couldn't open the filed copy: " + e.message); }
  }
  // Re-scan the SIGNED & READY folder — dry-run first, show the matches, confirm,
  // then apply. Never flips anything without the human seeing the counts.
  async function rescanSigned() {
    setRescanning(true); setRescanMsg(null);
    try {
      const dr = await fetch(`/api/market?fn=dropbox&action=scan-signed&dryRun=1`, { method: "POST", headers: getApiAuthHeaders() });
      const dd = await dr.json().catch(() => ({}));
      if (!dr.ok) throw new Error(dd.error || `HTTP ${dr.status}`);
      const would = (dd.matched || 0) - (dd.alreadySigned || 0);
      const unm = (dd.unmatched || []).length;
      if (!window.confirm(`Scan of "SIGNED & READY":\n\n${dd.scanned} file(s) found\n${dd.matched} matched to acreage reports (${dd.alreadySigned} already signed)\n${unm} unmatched\n\nMark ${would} newly-signed and file the signed copies?`)) {
        setRescanMsg(`Dry run: ${would} would be marked signed · ${unm} unmatched (nothing changed).`);
        setRescanning(false); return;
      }
      const ap = await fetch(`/api/market?fn=dropbox&action=scan-signed`, { method: "POST", headers: getApiAuthHeaders() });
      const ad = await ap.json().catch(() => ({}));
      if (!ap.ok) throw new Error(ad.error || `HTTP ${ap.status}`);
      setRescanMsg(`✓ Marked ${ad.flipped} signed · ${(ad.unmatched || []).length} unmatched.`);
      await load();
    } catch (e) { setRescanMsg("Scan failed: " + e.message); }
    setRescanning(false);
  }

  // ── Batch AI-fill ──────────────────────────────────────────────────────────
  // Same engine as the per-farmer button, driven over a checkbox selection. Mirrors
  // the ZIP import's bounded worker pool (2 workers + backoff + stagger): every
  // farmer costs one live Dropbox fetch AND an Anthropic 578 parse, and both
  // rate-limit, so this cannot fan out wide.
  //
  // The safety gates are NOT relaxed for batch. A farmer whose parse doesn't
  // reconcile to his own 578 (verify.ok === false) or whose form layout wasn't
  // recognized is HELD BACK for the one-at-a-time modal — never auto-stamped. These
  // forms go to an AIP on the farmer's behalf; a silent bad stamp is a real harm.
  const BATCH_CONCURRENCY = 2;
  // Worth another attempt: the API was busy, or it answered but the answer was
  // unusable. Both are transient. NOT retryable: a missing 578, an unreadable form
  // layout, a parse that didn't reconcile — those are stable facts about the data
  // and re-running just burns a parse to fail identically.
  const isRetryable = (m) => /rate limit|overloaded|529|too many|timed? ?out|returned nothing|invalid JSON|\b50[234]\b/i.test(m || "");
  const agoShort = (iso) => {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 3600000) return `${Math.max(1, Math.round(ms / 60000))}m ago`;
    if (ms < 172800000) return `${Math.round(ms / 3600000)}h ago`;
    return `${Math.round(ms / 86400000)}d ago`;
  };
  async function acrApi(action, payload) {
    const r = await fetch(`/api/market?fn=acreage-fill&action=${action}`, {
      method: "POST", headers: { ...getApiAuthHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(payload),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(typeof d.error === "string" ? d.error : `HTTP ${r.status}`);
    return d;
  }
  // Fetch + index the farmer's blank fillable form from Dropbox, same as the modal.
  async function stageFillable(row) {
    const r = await fetch("/api/market?fn=dropbox&action=stage-fillable", {
      method: "POST", headers: { ...getApiAuthHeaders(), "Content-Type": "application/json" },
      // Pass the row's state so a two-state farmer's batch fill targets THAT state's
      // form (scoped), not the largest — lets split rows be batched safely.
      body: JSON.stringify({ farmer_id: row.fid, name: row.name, crop_year: cur.year, state: row.state || undefined }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok || d.ok === false) throw new Error(d.error || `HTTP ${r.status}`);
    return d;
  }
  // One farmer, end to end. Throws only on transport/unknown errors (the pool
  // retries those); a clean "we won't fill this one" comes back as status:"held".
  async function fillOne(row, typeChoices) {
    const base = { fid: row.fid, name: row.name, county: row.county };
    const sd = await stageFillable(row);
    if (!sd.document_id) return { ...base, status: "held", msg: "No fillable acreage form found in Dropbox." };
    // Default the Dropbox push to Silas Filled — the outbox for every filled report.
    if (sd.silasFilledDest) _lastFilledDest = sd.silasFilledDest;
    // ONE call: the server parses the 578, reconciles, verifies acre-for-acre, and only then
    // stamps — require_verified makes it refuse rather than stamp when the parse
    // doesn't reconcile. Previewing first would parse the same 578 through Anthropic
    // twice, for a gate the server can apply itself.
    const payload = { form_document_id: sd.document_id, farmer_id: row.fid, require_verified: true, hold_on_mixed_use: true, hold_on_pp_only: true };
    // ⚠ A BATCH HAS NO HUMAN TO OVERRIDE THE 578 PICK. The modal shows the picker and
    // the agent can switch; here the server's auto-pick is final, so the season it
    // chooses on has to be the tab's, not a hardcoded "spring".
    if (cur && cur.season) payload.season = cur.season;
    if (typeChoices && Object.keys(typeChoices).length) payload.typeChoices = typeChoices;
    const fd = await acrApi("fill", payload);
    // The 578 reports both grain and silage acres for a crop that can be keyed
    // either way → the agent decides, we don't. Held unstamped with the acre split
    // so the choice is made against real numbers.
    if (fd.needsChoice) return { ...base, status: "choice", mixedUse: fd.mixedUse || [] };
    if (fd.ok === false) return { ...base, status: "held", msg: fd.error || "Fill blocked." };
    // Report what each mixed-use crop was actually keyed as, so a resolved choice is
    // visible per farmer rather than taken on trust.
    const rv = fd.review || {};
    const mixed = rv.mixedUse || [];
    // Always state how much actually landed on the page. "Filled" was able to mean
    // "wrote nothing on it" (Pepper, 2026-07-17) — showing the count means a fill
    // that did nothing can never again read as a success, even if some future gap
    // slips past the zero-stamp guard.
    const notes = [`${rv.stampCount ?? "?"} field${rv.stampCount === 1 ? "" : "s"} stamped`];
    if (rv.unfilledFormFieldCount) notes.push(`${rv.unfilledFormFieldCount} form field(s) left blank`);
    if ((rv.discrepancies || []).length) notes.push(`${rv.discrepancies.length} discrepancy(ies) flagged on the PDF`);
    // Share ≠ 100% on the 578 — no human is watching in a batch, so it rides along as
    // a per-farmer note. Still filled at 100% share; this is a confirm-me flag.
    const shareFlags = (rv.exceptions && rv.exceptions.shareNot100) || [];
    if (shareFlags.length) notes.push(`⚠ share <100% on 578 (${shareFlags.map((s) => `${s.producer || "operator"} ${s.pct}%`).join(", ")}) — filled at 100%`);
    // Multiple 578s on file — the fill auto-picked one; flag so a partial isn't used blind.
    const others578 = (rv.report578 && rv.report578.others) || [];
    if (others578.length) {
      const ch = rv.report578.chosen;
      notes.push(`⚠ ${others578.length + 1} 578s on file — used ${ch ? (ch.isNationwide ? "nationwide" : ch.county ? `${ch.county}-only` : (ch.filename || "one")) : "one"}; confirm it's the right 578`);
    }
    if (mixed.length) notes.push(mixed.map(m => `${m.crop} keyed ${m.choice === "reported" ? "as reported on the 578" : "all as grain"}`).join(" · "));
    return {
      ...base, status: "filled",
      docId: sd.document_id, // the blank form's row — where the Dropbox push is recorded
      path: (fd.filled && fd.filled.path) || null,
      url: (fd.filled && fd.filled.signedUrl) || null,
      stampCount: rv.stampCount ?? null,
      note: notes.join(" · "),
    };
  }
  const setChoice = (fid, crop, val) => setChoiceSel(c => ({ ...c, [fid]: { ...(c[fid] || {}), [crop]: val } }));
  // merge=true re-runs a subset (resolved mixed-use farmers, retried failures) and
  // folds the new outcomes into the existing results instead of wiping the board —
  // filled farmers keep their PDFs and their un-sent-to-Mandy state.
  async function runBatch(targets, choicesByFid = {}, merge = false) {
    if (!targets.length) return;
    const prior = (merge && batch && batch.results) || [];
    const keep = prior.filter(r => !targets.some(t => t.fid === r.fid));
    // choices ride along on batch state so "retry failed" reuses the same
    // grain/silage decisions instead of silently reverting to a default.
    setBatch(b => ({
      phase: "running", total: targets.length, done: 0, results: keep,
      log: merge && b ? b.log : [],
      choices: { ...((merge && b && b.choices) || {}), ...choicesByFid },
    }));
    let next = 0, done = 0;
    const results = [...keep];
    const worker = async () => {
      while (true) {
        const i = next++;
        if (i >= targets.length) break;
        const row = targets[i];
        let res = null;
        for (let attempt = 0; attempt < 4; attempt++) {
          try {
            if (attempt > 0) await new Promise(r => setTimeout(r, attempt * 20000)); // 20s / 40s / 60s
            res = await fillOne(row, choicesByFid[row.fid] || null);
            break;
          } catch (e) {
            res = { fid: row.fid, name: row.name, county: row.county, status: "error", msg: e.message };
            if (!isRetryable(e.message)) break;
          }
        }
        results.push(res);
        done++;
        const mark = res.status === "filled" ? "✓" : res.status === "choice" ? "❓" : res.status === "held" ? "⚠" : "✕";
        const line = res.status === "choice" ? `${mark} ${res.name} — 578 has both grain and silage; needs your call` : `${mark} ${res.name}${res.msg ? " — " + res.msg : ""}`;
        setBatch(b => b ? {
          ...b, done,
          results: results.slice().sort((x, y) => x.name.localeCompare(y.name)),
          log: [...b.log, line].slice(-80),
        } : b);
        await new Promise(r => setTimeout(r, 1200)); // stagger so we don't burst the APIs
      }
    };
    await Promise.all(Array.from({ length: Math.min(BATCH_CONCURRENCY, targets.length) }, worker));
    setBatch(b => b ? { ...b, phase: "done" } : b);
    setSel(new Set());
    await load(); // pick up the new filled_at stamps
  }
  // Email every successfully-filled PDF to Mandy, one at a time (Resend + a PDF
  // attachment each — no reason to parallelise).
  async function sendAllMandy() {
    const todo = ((batch && batch.results) || []).filter(r => r.status === "filled" && r.path && !r.sent);
    if (!todo.length) return;
    setSendingAll(true);
    for (const t of todo) {
      let patch;
      try {
        const d = await acrApi("email-filled", { filled_path: t.path, farmer_id: t.fid, farmer_name: t.name, county: t.county });
        patch = d.sent ? { sent: true, sendErr: null } : { sendErr: d.error || "email failed" };
      } catch (e) { patch = { sendErr: e.message }; }
      setBatch(b => b ? { ...b, results: b.results.map(r => r.fid === t.fid ? { ...r, ...patch } : r) } : b);
    }
    setSendingAll(false);
  }
  // File filled PDFs into a Dropbox folder the agent picked. Separate from the Mandy
  // email on purpose (Kevin 2026-07-17) — filing and notifying are different acts,
  // and you may want either, both, or neither.
  //
  // Driven from BOTH the batch results panel and the board, over the same targets
  // shape, because the results panel is thrown away on close while filled_at and the
  // PDF path persist — the board has to be able to file a farmer filled an hour ago.
  //
  // This can move farmers on the board: arStage is read from which Dropbox folder the
  // form sits in, so filing into a stage subfolder advances them. Hence the reload.
  async function pushRows(targets, dest) {
    const todo = (targets || []).filter(t => t.path);
    if (!todo.length) { setPushPicker(null); return; }
    setPushing(true);
    _lastFilledDest = dest;
    const failed = [];
    const stuck = [];
    let archived = 0;
    for (const t of todo) {
      let patch;
      try {
        const d = await acrApi("push-filled", { filled_path: t.path, dest_folder: dest, farmer_id: t.fid, farmer_name: t.name, form_document_id: t.docId || null });
        patch = d.ok ? { pushed: d.path || dest, pushErr: null } : { pushErr: d.error || "push failed" };
        // The filled copy filed but the signed original couldn't be cleared out of
        // Ready for Silas. Call it out separately from a failed push — the report
        // IS filed, so re-pushing would duplicate it; this just needs a hand-move.
        if (d.ok && d.sourceArchived) archived++;
        if (d.ok && d.sourceArchiveError) stuck.push(t.name);
      } catch (e) { patch = { pushErr: e.message }; }
      if (patch.pushErr) failed.push(`${t.name}: ${patch.pushErr}`);
      // Keep the batch panel in sync when the push came from there; harmless no-op
      // when it came from the board.
      setBatch(b => b ? { ...b, results: b.results.map(r => r.fid === t.fid ? { ...r, ...patch } : r) } : b);
    }
    setPushing(false);
    setPushPicker(null);
    if (failed.length) setRescanMsg(`⚠ ${failed.length} of ${todo.length} didn't file: ${failed.slice(0, 3).join(" · ")}`);
    else {
      const cleared = archived ? ` · cleared ${archived} from Ready for Silas` : "";
      const left = stuck.length ? ` · ⚠ still in Ready for Silas (move by hand): ${stuck.slice(0, 3).join(", ")}` : "";
      setRescanMsg(`✓ Filed ${todo.length} acreage report${todo.length === 1 ? "" : "s"} to ${dest}${cleared}${left}`);
    }
    await load(); // a folder move can change arStage — re-read the board
  }
  // Board rows → the {fid,name,county,path,docId} shape pushRows expects.
  const pushTargets = (rowsIn) => rowsIn.filter(r => r.filledPath).map(r => ({ fid: r.fid, name: r.name, county: r.county, path: r.filledPath, docId: r.filledDocId }));

  const chip = (bg, fg, txt, onClick) => <span onClick={onClick} style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: bg, color: fg, whiteSpace: "nowrap", cursor: onClick ? "pointer" : "default" }}>{txt}</span>;
  const srcChip = (src) => src === "box" ? chip("#eef6ec", "#2d6a1a", "📦 Box") : src === "dbx" || src === "dropbox" ? chip("#e8f0fa", "#1a4a7a", "🗂️ Dropbox") : src === "email" ? chip("#eef2fb", "#1a4a7a", "📧 Email") : null;
  const act = { fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, border: "1px solid #c8d8c0", background: "#fff", color: "#4a6e4a", cursor: "pointer" };
  const canBatch = !readOnly && cur.season !== "prf";
  const cols = canBatch ? "30px 1.7fr 1.1fr 1fr 1.4fr 1.2fr" : "1.7fr 1.1fr 1fr 1.4fr 1.2fr";

  const bRes = (batch && batch.results) || [];
  const bFilled = bRes.filter(r => r.status === "filled");
  const bChoice = bRes.filter(r => r.status === "choice"); // 578 mixes grain + silage — your call
  const bHeld = bRes.filter(r => r.status === "held");     // gates said no — a human must look
  const bErr = bRes.filter(r => r.status === "error");     // transport/unknown — retryable
  const bUnsent = bFilled.filter(r => r.path && !r.sent);
  const bUnpushed = bFilled.filter(r => r.path && !r.pushed);
  // Every mixed-use crop must be answered before those farmers can fill — no default.
  const allChosen = bChoice.length > 0 && bChoice.every(r => (r.mixedUse || []).every(m => (choiceSel[r.fid] || {})[m.crop]));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {PB_SEASONS.map(s => (
            <button key={s.key} onClick={() => setSeasonKey(s.key)} style={{ border: "1px solid #dde8d8", borderBottom: seasonKey === s.key ? "3px solid #2d4a2d" : "3px solid transparent", background: seasonKey === s.key ? "#eef6ec" : "#fff", borderRadius: "9px 9px 0 0", padding: "8px 16px", fontSize: 13, fontWeight: 700, color: seasonKey === s.key ? "#1a2e1a" : "#60735f", cursor: "pointer" }}>
              {s.label} <span style={{ fontSize: 10, color: "#9bab97" }}>{s.sub}</span>
            </button>
          ))}
        </div>
        {!readOnly && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* Selections survive the search, so say so — otherwise farmers you picked
              under an earlier query are about to be filled with nothing on screen
              showing it. */}
          {canBatch && selRows.length > 0 && (
            <span style={{ fontSize: 11, color: "#4a6e4a", display: "flex", alignItems: "center", gap: 6 }}>
              <b>{selRows.length}</b> selected{selHidden > 0 ? ` · ${selHidden} not shown by this search` : ""}
              <button onClick={() => setSel(new Set())} style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 6, border: "1px solid #c8d8c0", background: "#fff", color: "#60735f", cursor: "pointer" }}>clear</button>
            </span>
          )}
          {canBatch && (
            <button onClick={() => { setChoiceSel({}); runBatch(selRows); }} disabled={!selRows.length}
              title={selRows.length ? `AI-fill ${selRows.length} selected farmer(s)` : "Tick farmers in the Acreage Report column to batch-fill them"}
              style={{ fontSize: 12, fontWeight: 700, padding: "8px 14px", borderRadius: 8, border: "1px solid #1f6b1a", background: selRows.length ? "#1f6b1a" : "#f2f6f2", color: selRows.length ? "#fff" : "#9bab97", cursor: selRows.length ? "pointer" : "not-allowed" }}>
              🌾 Batch AI-fill{selRows.length ? ` (${selRows.length})` : ""}
            </button>
          )}
          {/* Filter to AI-filled → select all → file the lot. Only counts selected
              farmers that actually have a stamped PDF to file. */}
          {canBatch && selPushable.length > 0 && (
            <button onClick={() => setPushPicker({ targets: pushTargets(selPushable) })}
              title={`File ${selPushable.length} filled report(s) into a Dropbox folder`}
              style={{ fontSize: 12, fontWeight: 700, padding: "8px 14px", borderRadius: 8, border: "1px solid #1a4a7a", background: "#fff", color: "#1a4a7a", cursor: "pointer" }}>
              📁 File {selPushable.length} to Dropbox
            </button>
          )}
          <button onClick={rescanSigned} disabled={rescanning} style={{ fontSize: 12, fontWeight: 700, padding: "8px 14px", borderRadius: 8, border: "1px solid #c8d8c0", background: "#fff", color: "#3a6e32", cursor: rescanning ? "wait" : "pointer" }}>
            {rescanning ? "Scanning…" : "🔄 Re-scan signed"}
          </button>
        </div>
        )}
      </div>
      {cur.season !== "prf" && (
        <div style={{ marginBottom: 12 }}>
          {/* ⚠ `width: 100%` PLUS PADDING IS WIDER THAN 100%. Without border-box the
              28px of padding and 2px of border are ADDED to the parent's width, so this
              ended 8px past a 390px phone and dragged the whole page sideways with it —
              measured 2026-09-24, one of three culprits on this screen. */}
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search farmer or county…"
            style={{ width: "100%", maxWidth: 360, boxSizing: "border-box", padding: "9px 14px", border: "1px solid #c8d8c0", borderRadius: 9, fontSize: 13 }} />
        </div>
      )}
      {rescanMsg && <div style={{ fontSize: 12, color: "#3a4a38", background: "#eef4fb", border: "1px solid #d6e4f5", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>{rescanMsg}</div>}
      {stageErr && <div style={{ fontSize: 11, color: "#8a3a00", background: "#fdf0e0", border: "1px solid #f0d6b0", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>⚠ Couldn't read Dropbox folder stages ({stageErr}) — the Acreage Report / Signed columns may be incomplete.</div>}
      {!stageErr && stageListingErrors.length > 0 && <div style={{ fontSize: 11, color: "#a03028", background: "#fdf1f0", border: "1px solid #f0c9c5", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>⚠ Couldn't read {stageListingErrors.length === 1 ? "a Dropbox folder" : `${stageListingErrors.length} Dropbox folders`} ({stageListingErrors.map(x => String(x.folder || "").split("/").pop() || x.folder).join(" · ")}) — farms that may be further along show "stage unknown".</div>}

      {cur.season !== "prf" && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
          {PB_STATS.map(s => {
            const n = stats[s.key] || 0;
            const isAll = s.key === "total";
            const active = statFilter === s.key && !isAll;
            const clickable = isAll ? !!statFilter : n > 0;
            return (
              <button key={s.key}
                onClick={() => setStatFilter(isAll ? null : (active ? null : s.key))}
                disabled={!clickable && !active}
                title={isAll ? (statFilter ? "Show all farmers" : "Showing all farmers") : n ? `Show only these ${n}` : "None in this group"}
                style={{
                  background: active ? "#eef6ec" : "#fff",
                  border: `1px solid ${active ? "#1f6b1a" : "#dde8d8"}`,
                  boxShadow: active ? "0 0 0 2px rgba(31,107,26,.16)" : "none",
                  borderRadius: 10, padding: "10px 16px", minWidth: 110, textAlign: "left",
                  cursor: clickable || active ? "pointer" : "default",
                  opacity: !clickable && !active ? 0.5 : 1,
                  font: "inherit",
                }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{n}</div>
                {/* The step number, so the row reads as a sequence rather than as eight
                    unrelated counters. "Farmers" carries none — it is the clear-filter
                    button, not a stage. See PB_STATS for why the ORDER is load-bearing. */}
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#4a6e4a" }}>
                  {s.step != null && <span style={{ color: "#9aad9a" }}>{s.step} · </span>}{s.label}
                </div>
              </button>
            );
          })}
          {statFilter && (
            <span style={{ fontSize: 11.5, color: "#4a6e4a" }}>
              Showing <b>{view.length}</b> of {rows.length}
              {q.trim() ? <> matching “{q.trim()}”</> : null} ·{" "}
              <button onClick={() => setStatFilter(null)} style={{ border: "none", background: "none", color: "#1f6b1a", fontWeight: 700, fontSize: 11.5, cursor: "pointer", padding: 0, textDecoration: "underline" }}>clear filter</button>
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#60735f", fontSize: 15 }}>Loading pipeline…</div>
      ) : cur.season === "prf" ? (
        <PRFPipelineBoard year={cur.year} />
      ) : rows.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 12, padding: "40px 24px", textAlign: "center", color: "#60735f" }}>
          No {cur.label.replace(/[^A-Za-z]/g, "")} {cur.year} farmers yet — this fills in as 578s and acreage reports come in for the season.
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10 }}>
          {!isMobile && (
            <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, background: "#eef4ea", borderBottom: "1px solid #c8d8c0", padding: "9px 16px", fontSize: 10, fontWeight: 800, color: "#4a6e4a", letterSpacing: "0.05em", textTransform: "uppercase", position: "sticky", top: 0, zIndex: 5, boxShadow: "0 2px 6px rgba(30,60,30,0.06)" }}>
              {canBatch && <div><input type="checkbox" checked={allSel} onChange={toggleAll} disabled={!needing.length} title={`Select all ${needing.length} farmer(s) needing a fill`} style={{ cursor: needing.length ? "pointer" : "not-allowed" }} /></div>}
              <div onClick={() => toggleSort("name")} style={{ cursor: "pointer" }}>Farmer{arrow("name")}</div>
              <div onClick={() => toggleSort("p578")} style={{ cursor: "pointer" }}>578{arrow("p578")}</div>
              <div onClick={() => toggleSort("mandy")} style={{ cursor: "pointer" }}>578 → Mandy{arrow("mandy")}</div>
              <div onClick={() => toggleSort("ar")} style={{ cursor: "pointer" }}>Acreage Report{arrow("ar")}</div>
              <div onClick={() => toggleSort("signed")} style={{ cursor: "pointer" }}>Status{arrow("signed")}</div>
            </div>
          )}
          {view.map((row, i) => {
            const cell578 = row.r578 ? <>{chip("#e8f4e2", "#2d6a1a", "✅")}{srcChip(row.r578.source)}</> : chip("#fdecea", "#c0392b", "⚠ missing");
            const cellMandy = !row.r578 ? <span style={{ color: "#c8d2c6" }}>—</span> : row.r578.forwarded_to_mandy ? chip("#e0f0e4", "#1a5a2a", "✅ sent") : readOnly ? chip("#fdf6e0", "#8a6a00", "not sent") : <button onClick={() => forwardMandy(row)} disabled={busyFwd[row.r578.id]} style={act}>{busyFwd[row.r578.id] ? "…" : "✉ Send 578"}</button>;
            // The filled PDF is a button, not a chip — "✓ filled 2h ago" that happens
            // to be clickable is undiscoverable, and opening it is the whole point of
            // the AI-filled group.
            const filledBtn = row.filledAt && row.filledPath
              ? <button onClick={() => viewDoc(row.filledPath)} title={`Open the AI-filled PDF (${new Date(row.filledAt).toLocaleString()})`}
                  style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: "#eef2fb", color: "#1a4a7a", border: "1px solid #d6e4f5", cursor: "pointer", whiteSpace: "nowrap" }}>
                  📄 filled {agoShort(row.filledAt)}
                </button>
              : row.filledAt ? chip("#eef2fb", "#1a4a7a", `✓ filled ${agoShort(row.filledAt)}`) : null;
            // File this one to Dropbox, straight from the board — the batch panel is
            // gone once you close it, so a farmer filled an hour ago still needs a way.
            // "filed" used to be a dead chip — clicking it (the obvious thing to do
            // when you want to see what Mandy will key) did nothing. It now opens the
            // copy that actually sits in Dropbox.
            const fileBtn = row.filledPath && !readOnly
              ? (row.pushedAt
                  ? (row.pushedPath
                      ? <button onClick={() => viewDropbox(row.pushedPath)} title={`Open the copy filed in Dropbox (${new Date(row.pushedAt).toLocaleString()})`}
                          style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: "#e8f4e2", color: "#2d6a1a", border: "1px solid #cde3c4", cursor: "pointer", whiteSpace: "nowrap" }}>
                          📁 filed {agoShort(row.pushedAt)}
                        </button>
                      : chip("#e8f4e2", "#2d6a1a", `📁 filed ${agoShort(row.pushedAt)}`))
                  : <button onClick={() => setPushPicker({ targets: pushTargets([row]) })} title="File this filled report into a Dropbox folder" style={act}>📁 File</button>)
              : null;
            // Opening the ORIGINAL (unfilled) form — the fallback when there's no
            // stamped PDF to show.
            const viewOriginal = row.arCount > 0
              ? <button onClick={() => viewDoc(row.ars[0].signed_storage_path || row.ars[0].storage_path)} style={act}>📄 View</button>
              : <span style={{ color: "#c8d2c6" }}>—</span>;
            // The filled PDF must be openable from EVERY group, not just the fillable
            // ones. This used to hang off row.canFill, which is false for a farmer in
            // Silas Filled by definition (he's waiting on Mandy) — so the one group
            // you most want to read the finished report from was the one group that
            // couldn't, and it showed a "View" of the blank original instead.
            const cellAR = readOnly
              ? (row.filledAt ? chip("#eef2fb", "#1a4a7a", `✓ filled ${agoShort(row.filledAt)}`)
                : row.canFill ? chip("#fdf6e0", "#8a6a00", "🕓 needs fill") : viewOriginal)
              : <>
                  {filledBtn}
                  {fileBtn}
                  {/* The AI fill is season-aware now: fillCropsFor() stamps the spring
                      crops on a spring report and the fall small grains on a fall one,
                      off the SAME season that picks the 578. The "fall — fill by hand"
                      gate that stood here is gone, because the thing it described is
                      fixed rather than merely hidden. */}
                  {(row.canFill || row.canRefill || row.canRerun) && <AcreageFillButton farmerId={row.fid} farmerName={row.name} county={row.county} cropYear={cur.year} fillSeason={cur.season} formState={row.state || null} onFilled={load} label={(row.filledAt || row.canRefill || row.canRerun) ? "🌾 Re-fill" : "🌾 AI-fill"} btnStyle={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, border: "1px solid #1f6b1a", background: "#eef7ea", color: "#1f6b1a", cursor: "pointer" }} />}
                  {!row.filledAt && !row.canFill && !row.canRefill && viewOriginal}
                </>;
            // signed_ready is no longer terminal — it means signed, still awaiting
            // keying (see the AR_RANK note above), so it reads as work, not as done.
            // Silas Filled wins the chip over Ready for Silas: once a report is filed
            // it's waiting on Mandy, so a signed copy still lingering in Ready for Silas
            // is stale and shouldn't keep the row reading as "ready to fill". A farmer
            // in Ready for Silas but NOT yet filed still reads as the actionable state.
            // With "AI-filled" and "Silas Filled" merged into one card, the row chip is
            // where filed-vs-not-yet-filed lives — and it's the actionable half:
            // "needs filing" means the stamped PDF exists but hasn't been pushed to the
            // Dropbox outbox, so Mandy can't see it yet.
            const cellStatus = row.inSilasFilled ? chip("#e0f0e4", "#1a5a2a", "🌾 filled · needs keying")
              : row.inReadyForSilas ? chip("#eef7ea", "#1f6b1a", "🌾 ready for Silas")
              : row.isDone ? chip("#e8f4e2", "#2d6a1a", "✅ done · keyed")
              : row.stageUnknown ? chip("#fdf1f0", "#a03028", "? stage unknown")
              : row.arStage === "silas_filled" ? chip("#e0f0e4", "#1a5a2a", "🌾 filled · needs keying")
              : (row.filledAt && !row.pushedAt) ? chip("#fdf6e0", "#8a6a00", "🌾 filled · needs filing")
              : (row.arStage === "signed_ready" || row.arStage === "signed_pending") ? chip("#eef2fb", "#1a4a7a", "🕓 signed · needs keying")
              : row.arStage === "need_signed" ? chip("#fdf6e0", "#8a6a00", "✍️ awaiting signature")
              : <span style={{ color: "#c8d2c6" }}>—</span>;
            // Two-state (split) rows are filled PER STATE from their own Fill button,
            // never batched — the batch stages by farmer and would fill the largest
            // form, not this state's. So the batch checkbox is single-state rows only.
            const cellSel = canBatch ? <div>{row.canFill ? <input type="checkbox" checked={sel.has(row.key)} onChange={() => toggleSel(row.key)} style={{ cursor: "pointer" }} /> : null}</div> : null;
            if (isMobile) {
              const lbl = { fontSize: 10.5, fontWeight: 800, color: "#9bab97", letterSpacing: "0.05em", textTransform: "uppercase" };
              return (
                <div key={row.key} style={{ padding: "12px 14px", borderBottom: i < view.length - 1 ? "1px solid #edf2e8" : "none", background: sel.has(row.key) ? "#f4faf1" : undefined }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {canBatch && row.canFill && <input type="checkbox" checked={sel.has(row.key)} onChange={() => toggleSel(row.key)} style={{ cursor: "pointer" }} />}
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a" }}>{row.name}</div>
                    {row.state && <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: "#eaf1fb", color: "#1a4a7a" }}>{row.state}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: "#60735f", marginBottom: 8 }}>{row.state ? `${row.state} state report` : `${row.county || "—"} County`}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: "7px 10px", alignItems: "center" }}>
                    <span style={lbl}>578</span><div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>{cell578}</div>
                    <span style={lbl}>→ Mandy</span><div>{cellMandy}</div>
                    <span style={lbl}>Acreage</span><div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>{cellAR}</div>
                    <span style={lbl}>Status</span><div>{cellStatus}</div>
                  </div>
                </div>
              );
            }
            return (
              <div key={row.key} style={{ display: "grid", gridTemplateColumns: cols, gap: 8, padding: "11px 16px", borderBottom: i < view.length - 1 ? "1px solid #edf2e8" : "none", background: sel.has(row.key) ? "#f4faf1" : i % 2 ? "#fafcf8" : "#fff", alignItems: "center" }}>
                {cellSel}
                <div><div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", display: "flex", alignItems: "center", gap: 6 }}>{row.name}{row.state && <span title={`${row.state} acreage report`} style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: "#eaf1fb", color: "#1a4a7a", letterSpacing: "0.04em" }}>{row.state}</span>}</div><div style={{ fontSize: 10, color: "#60735f" }}>{row.state ? `${row.state} state report` : `${row.county || "—"} County`}</div></div>
                <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>{cell578}</div>
                <div>{cellMandy}</div>
                <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>{cellAR}</div>
                <div>{cellStatus}</div>
              </div>
            );
          })}
          {view.length === 0 && <div style={{ padding: "30px", textAlign: "center", color: "#60735f" }}>No farmer matches “{q}”.</div>}
        </div>
      )}

      {pushPicker && (
        <DropboxFolderPicker
          initialPath={_lastFilledDest}
          title={`File ${pushPicker.targets.length} filled report${pushPicker.targets.length === 1 ? "" : "s"} to Dropbox`}
          confirmLabel={`File ${pushPicker.targets.length} here`}
          busy={pushing}
          onPick={(dest) => pushRows(pushPicker.targets, dest)}
          onClose={() => setPushPicker(null)}
        />
      )}

      {batch && (
        <div onClick={() => { if (batch.phase === "done" && !sendingAll && !pushing) setBatch(null); }} style={{ position: "fixed", inset: 0, background: "rgba(20,30,20,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, maxWidth: 720, width: "100%", maxHeight: "86vh", overflow: "auto", padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <h3 style={{ margin: 0, color: "#1a2e1a", fontSize: 16 }}>Batch AI-fill — {batch.total} farmer{batch.total === 1 ? "" : "s"}</h3>
              {batch.phase === "done" && !sendingAll && <button onClick={() => setBatch(null)} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#60735f" }}>✕</button>}
            </div>
            <div style={{ fontSize: 11, color: "#60735f", marginBottom: 12 }}>
              {batch.phase === "running"
                ? `${batch.done} of ${batch.total} · 2 parallel workers · each farmer is parsed from his own 578 and reconciled acre-for-acre before anything is stamped`
                : `Done — ${bFilled.length} filled${bChoice.length ? `, ${bChoice.length} need your call` : ""}, ${bHeld.length} held for review, ${bErr.length} failed`}
            </div>

            <div style={{ height: 8, background: "#eef4ea", borderRadius: 5, overflow: "hidden", marginBottom: 14 }}>
              <div style={{ height: "100%", width: `${batch.total ? (batch.done / batch.total) * 100 : 0}%`, background: "#1f6b1a", transition: "width .3s" }} />
            </div>

            {batch.phase === "done" && bChoice.length > 0 && (
              <div style={{ border: "1px solid #f0d6b0", background: "#fffdf6", borderRadius: 10, padding: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#8a6a00", marginBottom: 2 }}>❓ Your call — {bChoice.length} 578{bChoice.length === 1 ? " reports" : "s report"} both grain and silage</div>
                <div style={{ fontSize: 11, color: "#7a6a40", lineHeight: 1.5, marginBottom: 10 }}>
                  Nothing was stamped for these. <b>As reported on 578</b> keys each field by the use the 578 gives it, so silage fields get the silage type. <b>All as GSG</b> keys every acre under the grain type; <b>All as GSS</b> keys every acre under silage.
                </div>
                {bChoice.map(r => (
                  <div key={r.fid} style={{ borderTop: "1px solid #f2e6c8", paddingTop: 8, marginTop: 8 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2e1a", marginBottom: 5 }}>{r.name}</div>
                    {(r.mixedUse || []).map(m => {
                      const cur2 = (choiceSel[r.fid] || {})[m.crop] || null;
                      const opt = (val, lbl) => (
                        <button key={val} onClick={() => setChoice(r.fid, m.crop, val)}
                          style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, border: `1px solid ${cur2 === val ? "#1f6b1a" : "#c8d8c0"}`, background: cur2 === val ? "#1f6b1a" : "#fff", color: cur2 === val ? "#fff" : "#60735f", cursor: "pointer" }}>{lbl}</button>
                      );
                      return (
                        <div key={m.crop} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 5 }}>
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#4a6e4a", minWidth: 62 }}>{m.crop}</span>
                          <span style={{ fontSize: 10.5, color: "#60735f", flex: 1, minWidth: 150 }}>
                            578 reports <b>{fmt(m.grainAcres, 1)} ac grain</b> · <b>{fmt(m.silageAcres, 1)} ac silage</b>
                          </span>
                          {opt("reported", "As reported on 578")}
                          {opt("grain", "All as GSG")}
                          {m.silageText && opt("silage", "All as GSS")}
                        </div>
                      );
                    })}
                  </div>
                ))}
                <button onClick={() => runBatch(rows.filter(r => bChoice.some(c => c.fid === r.fid)), choiceSel, true)} disabled={!allChosen}
                  title={allChosen ? "" : "Pick grain or as-reported for every crop above first"}
                  style={{ marginTop: 10, fontSize: 12, fontWeight: 700, padding: "8px 14px", borderRadius: 8, border: "1px solid #1f6b1a", background: allChosen ? "#1f6b1a" : "#f2f6f2", color: allChosen ? "#fff" : "#9bab97", cursor: allChosen ? "pointer" : "not-allowed" }}>
                  🌾 Fill {bChoice.length} with these choices
                </button>
              </div>
            )}

            {/* ── FINALIZE ────────────────────────────────────────────────────
                The run is over; this is where you find out what happened and file
                the results. Filing used to mean opening a folder browser, but the
                answer is ALWAYS Silas Filled — so that's a one-click primary action
                now, with the browser kept as a secondary escape hatch. */}
            {batch.phase === "done" && (() => {
              const total = bRes.length;
              const attention = bChoice.length + bHeld.length + bErr.length;
              const pushTgts = bUnpushed.map(r => ({ fid: r.fid, name: r.name, county: r.county, path: r.path, docId: r.docId }));
              // stage-fillable hands back the Silas Filled path on every lookup, and
              // fillOne stashes it; only offer one-click filing when we actually have it.
              const silasDest = _lastFilledDest && /Silas Filled\s*$/i.test(_lastFilledDest) ? _lastFilledDest : null;
              const allFiled = bFilled.length > 0 && bUnpushed.length === 0;
              const banner = bFilled.length === total && total > 0
                ? { bg: "#e3f3e0", fg: "#1f6b1a", txt: `✓ All ${total} filled successfully` }
                : bFilled.length === 0
                  ? { bg: "#fbeae7", fg: "#a02c1a", txt: `Nothing was filled — ${attention} need${attention === 1 ? "s" : ""} your attention` }
                  : { bg: "#fdf6e0", fg: "#8a6a00", txt: `${bFilled.length} of ${total} filled · ${attention} need${attention === 1 ? "s" : ""} your attention` };
              return (
                <>
                  <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontWeight: 800, fontSize: 13.5, background: banner.bg, color: banner.fg }}>
                    {banner.txt}
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                    {[["Filled", bFilled.length, "#2d6a1a"], ["Your call", bChoice.length, "#8a6a00"], ["Held for review", bHeld.length, "#8a6a00"], ["Failed", bErr.length, "#c0392b"]].map(([l, n, c]) => (
                      <div key={l} style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, padding: "8px 14px", minWidth: 96 }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: c }}>{n}</div>
                        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#4a6e4a" }}>{l}</div>
                      </div>
                    ))}
                  </div>

                  {/* the finalize step itself */}
                  {bUnpushed.length > 0 && (
                    <div style={{ border: "1px solid #cde3c4", background: "#f4faf1", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: "#1f6b1a", marginBottom: 2 }}>Finish up — file {bUnpushed.length} report{bUnpushed.length === 1 ? "" : "s"} for Mandy</div>
                      <div style={{ fontSize: 11, color: "#4a6e4a", marginBottom: 10, lineHeight: 1.5 }}>
                        Filing puts the stamped report in <b>Silas Filled</b> and clears the signed original out of Ready for Silas, so the queue reflects what's left.
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button onClick={() => (silasDest ? pushRows(pushTgts, silasDest) : setPushPicker({ targets: pushTgts }))} disabled={pushing}
                          style={{ fontSize: 12.5, fontWeight: 800, padding: "9px 16px", borderRadius: 8, border: "1px solid #1f6b1a", background: pushing ? "#f2f6f2" : "#1f6b1a", color: pushing ? "#9bab97" : "#fff", cursor: pushing ? "wait" : "pointer" }}>
                          {pushing ? "Filing…" : `📁 Move ${bUnpushed.length} to Silas Filled`}
                        </button>
                        {silasDest && (
                          <button onClick={() => setPushPicker({ targets: pushTgts })} disabled={pushing}
                            style={{ fontSize: 11.5, fontWeight: 700, padding: "8px 12px", borderRadius: 8, border: "1px solid #c8d8c0", background: "#fff", color: "#60735f", cursor: pushing ? "wait" : "pointer" }}>
                            choose a different folder…
                          </button>
                        )}
                        {bUnsent.length > 0 && (
                          <button onClick={sendAllMandy} disabled={sendingAll || pushing}
                            style={{ fontSize: 11.5, fontWeight: 700, padding: "8px 12px", borderRadius: 8, border: "1px solid #c8d8c0", background: "#fff", color: "#4a6e4a", cursor: sendingAll ? "wait" : "pointer" }}>
                            {sendingAll ? "Sending…" : `✉ also email ${bUnsent.length} to Mandy`}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {allFiled && (
                    <div style={{ border: "1px solid #cde3c4", background: "#e8f4e2", borderRadius: 10, padding: "11px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: "#1f6b1a" }}>✓ All {bFilled.length} filed to Silas Filled — ready for Mandy to key.</div>
                      <button onClick={() => setBatch(null)} style={{ fontSize: 11.5, fontWeight: 700, padding: "7px 14px", borderRadius: 8, border: "1px solid #1f6b1a", background: "#fff", color: "#1f6b1a", cursor: "pointer" }}>Done</button>
                    </div>
                  )}

                  {bErr.length > 0 && !sendingAll && !pushing && (
                    <div style={{ marginBottom: 14 }}>
                      <button onClick={() => runBatch(rows.filter(r => bErr.some(e => e.fid === r.fid)), batch.choices || {})} style={{ fontSize: 12, fontWeight: 700, padding: "8px 14px", borderRadius: 8, border: "1px solid #c8d8c0", background: "#fff", color: "#4a6e4a", cursor: "pointer" }}>
                        ↺ Retry {bErr.length} failed
                      </button>
                    </div>
                  )}
                </>
              );
            })()}

            {bRes.length > 0 && (
              <div style={{ border: "1px solid #dde8d8", borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
                {bRes.map((r, i) => (
                  <div key={r.fid} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 12px", borderBottom: i < bRes.length - 1 ? "1px solid #edf2e8" : "none", background: i % 2 ? "#fafcf8" : "#fff" }}>
                    <span style={{ fontSize: 13 }}>{r.status === "filled" ? "✅" : r.status === "choice" ? "❓" : r.status === "held" ? "⚠️" : "❌"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2e1a" }}>{r.name}</div>
                      {(r.msg || r.note || r.sendErr || r.pushErr || r.status === "choice") && <div style={{ fontSize: 10.5, color: r.status === "filled" ? "#8a6a00" : "#60735f" }}>{r.sendErr ? `Email failed: ${r.sendErr}` : r.pushErr ? `Dropbox: ${r.pushErr}` : r.status === "choice" ? "578 mixes grain + silage — choose above" : r.msg || r.note}</div>}
                    </div>
                    {r.sent && chip("#e0f0e4", "#1a5a2a", "✉ sent")}
                    {r.pushed && chip("#e8f4e2", "#2d6a1a", "📁 filed")}
                    {r.status === "filled" && r.path && <button onClick={() => viewDoc(r.path)} style={act}>📄 View</button>}
                    {r.status === "held" && <AcreageFillButton farmerId={r.fid} farmerName={r.name} county={r.county} cropYear={cur.year} fillSeason={cur.season} onFilled={load} label="🌾 Open" btnStyle={act} />}
                  </div>
                ))}
              </div>
            )}

            {bHeld.length > 0 && batch.phase === "done" && (
              <div style={{ fontSize: 11, color: "#8a6a00", background: "#fdf6e0", border: "1px solid #f0e0b0", borderRadius: 8, padding: "8px 12px" }}>
                ⚠ {bHeld.length} farmer{bHeld.length === 1 ? " was" : "s were"} held back — the parse didn't reconcile to the 578, or the form layout wasn't recognized. Nothing was stamped for {bHeld.length === 1 ? "him" : "them"}. Open each one to resolve it by hand.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DOCUMENT LIBRARY  (the "Files" lens — a filterable cabinet over BOTH doc stores)
// Read-only index over farmer_578s (578s) + farmer_documents (acreage reports,
// SOIs, etc.). Two views: By Farmer (cabinet) and All Documents (flat list) with
// Type / Season / Year / County / Status filters + search. Opens the stored copy
// via a signed URL and surfaces the Dropbox path. Crop filter intentionally
// omitted (crop isn't stored per-document). Season/county for farmer_documents
// are derived from the farmer's 578s (those tables don't carry them).
// ─────────────────────────────────────────────────────────────────────────────
const LIB_TYPE_LABELS = { "578": "578", acreage_report: "Acreage Report", schedule_of_insurance: "Schedule of Insurance", other: "Other" };
const LIB_STATUS_COLORS = {
  "Signed": ["#e8f4e2", "#2d6a1a"], "On file": ["#e8f0fa", "#1a4a7a"], "Forwarded": ["#e0f0e4", "#1a5a2a"],
  "Matched": ["#fdf6e0", "#8a6a00"], "Unmatched": ["#fdecea", "#c0392b"], "Locked": ["#fdf0e0", "#8a3a00"],
  "Sent for signature": ["#eef2fb", "#1a4a7a"],
};

function DocumentLibrary({ farmers, readOnly = false }) {
  const [f578, setF578] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("byFarmer"); // byFarmer | allDocs
  const [q, setQ] = useState("");
  const [ff, setFf] = useState({ type: "all", season: "all", year: "all", county: "all", status: "all" });
  const [detailId, setDetailId] = useState(null);
  const [dbxFiles, setDbxFiles] = useState(null); // live Dropbox search results for the open farmer
  const [dbxLoading, setDbxLoading] = useState(false);
  const [dbxErr, setDbxErr] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [xtraFarmers, setXtraFarmers] = useState([]); // book-wide farmers self-loaded when the prop is empty (portal viewer)

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [a, b] = await Promise.all([sbFetch("farmer_578s"), sbFetch("farmer_documents")]);
        setF578(a || []); setDocs(b || []);
        if (!(farmers || []).length) { try { setXtraFarmers(await sbFetch("farmers", { select: "id,name" }) || []); } catch (e2) {} }
      } catch (e) { console.error("Document library load failed:", e); }
      setLoading(false);
    })();
  }, []);

  const farmerMap = useMemo(() => { const m = {}; [...(farmers || []), ...xtraFarmers].forEach(f => { if (f.id) m[f.id] = f; }); return m; }, [farmers, xtraFarmers]);
  const farmerName = (id) => farmerMap[id]?.name || "Unmatched";

  // farmer_documents carries no season/county — derive from the farmer's 578s.
  const derived = useMemo(() => {
    const county = {}, season = {};
    (f578 || []).forEach(r => {
      if (!r.farmer_id) return;
      if (r.county && !county[r.farmer_id]) county[r.farmer_id] = r.county;
      if (r.season) season[`${r.farmer_id}|${r.tax_year}`] = r.season;
    });
    return { county, season };
  }, [f578]);

  const items = useMemo(() => {
    const out = [];
    (f578 || []).forEach(r => out.push({
      key: "578-" + r.id, farmerId: r.farmer_id, type: "578", year: r.tax_year, season: r.season || null,
      county: r.county || null, title: r.original_filename || r.extracted_name || "578",
      storagePath: r.storage_path, dropboxPath: null, when: r.received_at, source: r.source || "manual",
      status: r.status === "password_protected" ? "Locked" : r.forwarded_to_mandy ? "Forwarded" : r.farmer_id ? "Matched" : "Unmatched",
    }));
    (docs || []).forEach(d => {
      const sig = d.signature_status;
      out.push({
        key: "doc-" + d.id, farmerId: d.farmer_id, type: d.doc_type || "other", year: d.crop_year,
        season: derived.season[`${d.farmer_id}|${d.crop_year}`] || null, county: derived.county[d.farmer_id] || null,
        title: d.title || d.original_filename || "Document",
        storagePath: d.signed_storage_path || d.storage_path, dropboxPath: d.dropbox_path,
        when: d.created_at, source: d.source || "manual_upload",
        status: sig === "signed" ? "Signed" : sig === "signed_copy_missing" ? "Signed — copy not on file" : sig === "sent" ? "Sent for signature" : "On file",
      });
    });
    return out.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));
  }, [f578, docs, derived]);

  const opts = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean))];
    return {
      type: uniq(items.map(i => i.type)),
      season: ["spring", "fall"],
      year: uniq(items.map(i => i.year)).sort((a, b) => b - a),
      county: uniq(items.map(i => i.county)).sort(),
      status: uniq(items.map(i => i.status)).sort(),
    };
  }, [items]);

  const matches = (i) => {
    const s = q.trim().toLowerCase();
    if (ff.type !== "all" && i.type !== ff.type) return false;
    if (ff.season !== "all" && i.season !== ff.season) return false;
    if (ff.year !== "all" && String(i.year) !== ff.year) return false;
    if (ff.county !== "all" && i.county !== ff.county) return false;
    if (ff.status !== "all" && i.status !== ff.status) return false;
    if (s && !(`${farmerName(i.farmerId)} ${i.title} ${i.county || ""}`.toLowerCase().includes(s))) return false;
    return true;
  };
  const shown = useMemo(() => items.filter(matches), [items, q, ff, farmerMap]);
  const anyFilter = q || Object.values(ff).some(v => v !== "all");

  async function openDoc(e, path) {
    if (e) e.stopPropagation();
    if (!path) { alert("No stored copy is on file for this document yet."); return; }
    try { const url = await sbStorageSignedUrl("farmer-documents", path); window.open(url, "_blank"); }
    catch (err) { alert("Couldn't open the document: " + err.message); }
  }

  // Live Dropbox pull for the open farmer drawer — every file for them across all
  // folders/years, via files/search_v2 (farmer name + policy cores).
  useEffect(() => {
    if (!detailId || detailId === "__none__") { setDbxFiles(null); setDbxErr(null); return; }
    let cancelled = false;
    setDbxLoading(true); setDbxErr(null); setDbxFiles(null);
    const policies = (docs || []).filter(d => d.farmer_id === detailId).map(d => d.policy_number).filter(Boolean);
    (async () => {
      try {
        const r = await fetch("/api/market?fn=dropbox&action=search-farmer", {
          method: "POST", headers: { ...getApiAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ farmer_id: detailId, name: farmerName(detailId), policies }),
        });
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        setDbxFiles(d.files || []);
      } catch (e) { if (!cancelled) setDbxErr(e.message); }
      finally { if (!cancelled) setDbxLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [detailId]);

  async function openDbxFile(path) {
    try {
      const r = await fetch(`/api/market?fn=dropbox&action=temp-link&path=${encodeURIComponent(path)}`, { method: "POST", headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.link) throw new Error(d.error || "no link");
      window.open(d.link, "_blank");
    } catch (e) { alert("Couldn't open from Dropbox: " + e.message); }
  }

  // Pull the latest documents from Dropbox into the index (moved here from the
  // retired Documents tab, so the Library is the single doc cabinet).
  async function syncDropbox() {
    setSyncing(true);
    try {
      const r = await fetch("/api/market?fn=dropbox&action=sync", { method: "POST", headers: getApiAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      const rows = await sbFetch("farmer_documents");
      setDocs(rows || []);
      alert(`Dropbox sync: ${d.new || 0} new document${(d.new || 0) === 1 ? "" : "s"} indexed${d.scanned ? ` (${d.scanned} scanned)` : ""}.`);
    } catch (e) { alert("Dropbox sync failed: " + e.message + (/not connected/i.test(e.message) ? " — reconnect Dropbox from the Box/Sources area." : "")); }
    setSyncing(false);
  }

  const typeLabel = (t) => LIB_TYPE_LABELS[t] || t;
  const StatusChip = ({ st }) => {
    const [bg, fg] = LIB_STATUS_COLORS[st] || ["#eef2ee", "#60735f"];
    return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: bg, color: fg, whiteSpace: "nowrap" }}>{st}</span>;
  };
  const seaPill = (s) => s ? <span style={{ fontSize: 10.5, fontWeight: 800, padding: "2px 7px", borderRadius: 5, marginRight: 4, background: s === "fall" ? "#f4f0e8" : "#eef6ec", color: s === "fall" ? "#6b4f00" : "#2d4a2d" }}>{s === "fall" ? "FALL" : "SPRING"}</span> : null;

  const sel = (key, label, options, fmt) => (
    <select value={ff[key]} onChange={e => setFf(p => ({ ...p, [key]: e.target.value }))}
      style={{ padding: "8px 10px", border: "1px solid #c8d8c0", borderRadius: 8, fontSize: 12, background: "#fff", color: ff[key] !== "all" ? "#1a2e1a" : "#60735f", fontWeight: ff[key] !== "all" ? 700 : 500 }}>
      <option value="all">{label}: All</option>
      {options.map(o => <option key={o} value={o}>{fmt ? fmt(o) : o}</option>)}
    </select>
  );

  // group shown items by farmer for the By-Farmer view + the drawer
  const byFarmer = useMemo(() => {
    const g = {};
    shown.forEach(i => { const k = i.farmerId || "__none__"; (g[k] = g[k] || []).push(i); });
    return g;
  }, [shown]);
  const farmerIdsShown = useMemo(() =>
    Object.keys(byFarmer).sort((a, b) => farmerName(a).localeCompare(farmerName(b))), [byFarmer]);

  const detailItems = useMemo(() => detailId ? items.filter(i => (i.farmerId || "__none__") === detailId) : [], [detailId, items]);

  return (
    <div>
      {/* toolbar: search + view toggle */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search documents — farmer, county, filename…"
          style={{ flex: 1, minWidth: 220, padding: "10px 14px", border: "1px solid #c8d8c0", borderRadius: 9, fontSize: 13 }} />
        <div style={{ display: "inline-flex", border: "1px solid #c8d8c0", borderRadius: 9, overflow: "hidden", background: "#fff" }}>
          {[["byFarmer", "🗂️ By Farmer"], ["allDocs", "📄 All Documents"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setView(k)} style={{ border: "none", padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", background: view === k ? "#eef6ec" : "transparent", color: view === k ? "#1a2e1a" : "#60735f" }}>{lbl}</button>
          ))}
        </div>
        {!readOnly && <button onClick={syncDropbox} disabled={syncing} title="Pull the latest documents from Dropbox into the index" style={{ fontSize: 12, fontWeight: 700, padding: "9px 14px", borderRadius: 9, border: "1px solid #c8d8c0", background: "#fff", color: "#3a6e32", cursor: syncing ? "wait" : "pointer" }}>{syncing ? "Syncing…" : "🔄 Sync Dropbox"}</button>}
      </div>

      {/* filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        {sel("type", "Type", opts.type, typeLabel)}
        {sel("season", "Season", opts.season, s => s === "fall" ? "Fall" : "Spring")}
        {sel("year", "Year", opts.year)}
        {sel("county", "County", opts.county)}
        {sel("status", "Status", opts.status)}
        {anyFilter && <button onClick={() => { setQ(""); setFf({ type: "all", season: "all", year: "all", county: "all", status: "all" }); }}
          style={{ fontSize: 12, fontWeight: 700, padding: "8px 12px", borderRadius: 8, border: "1px solid #c8d8c0", background: "#fff", color: "#4a6e4a", cursor: "pointer" }}>✕ Clear</button>}
      </div>

      <div style={{ background: "#eef4fb", border: "1px solid #d6e4f5", borderRadius: 10, padding: "10px 14px", fontSize: 11.5, color: "#1a4a7a", marginBottom: 14 }}>
        🗂️ The app indexes your Dropbox cabinet — filter or search, then open the document. Dropbox stays the source of truth; the app keeps an indexed working copy and never deletes or overwrites anything in Dropbox.
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#60735f", fontSize: 15 }}>Loading documents…</div>
      ) : view === "allDocs" ? (
        /* ── flat list ── */
        <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2.2fr 1.3fr 1fr 1fr 0.7fr", gap: 8, background: "#f4f8f0", borderBottom: "1px solid #dde8d8", padding: "9px 16px", fontSize: 10, fontWeight: 800, color: "#4a6e4a", letterSpacing: "0.05em", textTransform: "uppercase" }}>
            <div>Document</div><div>Farmer</div><div>Season · Year</div><div>Status</div><div></div>
          </div>
          {shown.map((i, idx) => (
            <div key={i.key} onClick={() => setDetailId(i.farmerId || "__none__")}
              style={{ display: "grid", gridTemplateColumns: "2.2fr 1.3fr 1fr 1fr 0.7fr", gap: 8, padding: "11px 16px", borderBottom: idx < shown.length - 1 ? "1px solid #edf2e8" : "none", background: idx % 2 ? "#fafcf8" : "#fff", alignItems: "center", cursor: "pointer" }}>
              <div><div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2e1a" }}>{i.title}</div><div style={{ fontSize: 10, color: "#60735f" }}>{typeLabel(i.type)}</div></div>
              <div style={{ fontSize: 12, color: "#3a4a38" }}>{farmerName(i.farmerId)}<div style={{ fontSize: 10, color: "#60735f" }}>{i.county || "—"}</div></div>
              <div style={{ fontSize: 12, color: "#4a6e4a" }}>{seaPill(i.season)}{i.year || "—"}</div>
              <div><StatusChip st={i.status} /></div>
              <div>{i.storagePath && <button onClick={(e) => openDoc(e, i.storagePath)} style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, border: "1px solid #c8d8c0", background: "#fff", color: "#3a6e32", cursor: "pointer" }}>Open</button>}</div>
            </div>
          ))}
          {!shown.length && <div style={{ padding: "40px", textAlign: "center", color: "#60735f" }}>No documents match these filters.</div>}
          <div style={{ padding: "8px 16px", fontSize: 11, color: "#60735f", borderTop: "1px solid #edf2e8" }}>{shown.length} of {items.length} documents</div>
        </div>
      ) : (
        /* ── by farmer cards ── */
        <div>
          <div style={{ fontSize: 11, color: "#60735f", marginBottom: 8 }}>{farmerIdsShown.length} farmers · {shown.length} of {items.length} documents</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: 12 }}>
            {farmerIdsShown.map(fid => {
              const docsFor = byFarmer[fid];
              const years = [...new Set(docsFor.map(d => d.year).filter(Boolean))].sort((a, b) => b - a);
              return (
                <div key={fid} onClick={() => setDetailId(fid)}
                  style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 12, padding: "14px 16px", cursor: "pointer" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a", fontFamily: "'Lora',serif" }}>{fid === "__none__" ? "Unmatched" : farmerName(fid)}</div>
                  <div style={{ fontSize: 10, color: "#60735f" }}>{docsFor[0]?.county || "—"} County</div>
                  <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <span>📄 {docsFor.length} doc{docsFor.length === 1 ? "" : "s"}</span>
                    <span>🗂️ {years.slice(0, 5).map(y => <span key={y} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "#f0f4ef", color: "#5a7458", marginRight: 4 }}>{y}</span>)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {!farmerIdsShown.length && <div style={{ padding: "40px", textAlign: "center", color: "#60735f" }}>No documents match these filters.</div>}
        </div>
      )}

      {/* farmer drawer */}
      {detailId && (
        <div onClick={() => setDetailId(null)} style={{ position: "fixed", inset: 0, background: "rgba(20,30,20,.45)", display: "flex", justifyContent: "flex-end", zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: "94vw", height: "100%", background: "#f7faf5", overflowY: "auto", boxShadow: "-10px 0 40px rgba(0,0,0,.2)" }}>
            <div style={{ padding: "20px 22px", background: "#fff", borderBottom: "1px solid #dde8d8", position: "sticky", top: 0, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, color: "#1a2e1a", fontFamily: "'Lora',serif" }}>{detailId === "__none__" ? "Unmatched" : farmerName(detailId)}</h2>
                <div style={{ fontSize: 11, color: "#60735f", marginTop: 3 }}>{detailItems[0]?.county || ""} · everything on file, every year</div>
              </div>
              <button onClick={() => setDetailId(null)} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#60735f" }}>✕</button>
            </div>
            <div style={{ padding: "18px 22px" }}>
              {/* Live from Dropbox — every file for this farmer, all folders/years */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontFamily: "'Lora',serif", fontSize: 16, fontWeight: 700, color: "#1a2e1a" }}>🗂️ In Dropbox</span>
                {dbxFiles && <span style={{ fontSize: 11, color: "#60735f" }}>{dbxFiles.length} file{dbxFiles.length === 1 ? "" : "s"} · live</span>}
              </div>
              {dbxLoading && <div style={{ fontSize: 12, color: "#60735f", padding: "4px 0 12px" }}>Searching Dropbox…</div>}
              {dbxErr && <div style={{ fontSize: 12, color: "#8a3a00", background: "#fdf0e0", border: "1px solid #f0d6b0", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>⚠ Dropbox search failed: {dbxErr}</div>}
              {dbxFiles && !dbxFiles.length && !dbxLoading && <div style={{ fontSize: 12, color: "#60735f", padding: "4px 0 12px" }}>No files found in Dropbox for this farmer.</div>}
              {dbxFiles && dbxFiles.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  {dbxFiles.map((f, i) => (
                    <div key={i} style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 9, padding: "10px 13px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1a2e1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                        <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 2 }}>{f.folder || "—"}{f.year ? ` · ${f.year}` : ""}{f.modified ? ` · ${new Date(f.modified).toLocaleDateString()}` : ""}</div>
                      </div>
                      <button onClick={() => openDbxFile(f.path)} style={{ fontSize: 11, fontWeight: 700, padding: "5px 11px", borderRadius: 6, border: "1px solid #b9d0ee", background: "#eef4fb", color: "#1a4a7a", cursor: "pointer", whiteSpace: "nowrap" }}>Open ↗</button>
                    </div>
                  ))}
                </div>
              )}
              {detailItems.length > 0 && <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#9bab97", margin: "4px 0 -4px" }}>App status (indexed)</div>}
              {[...new Set(detailItems.map(i => i.year).filter(Boolean))].sort((a, b) => b - a).concat(detailItems.some(i => !i.year) ? ["—"] : []).map(yr => {
                const group = detailItems.filter(i => (i.year || "—") === yr);
                return (
                  <div key={yr}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0 8px" }}>
                      <span style={{ fontFamily: "'Lora',serif", fontSize: 16, fontWeight: 700, color: "#1a2e1a" }}>{yr}</span>
                      <div style={{ flex: 1, height: 1, background: "#dde8d8" }} />
                    </div>
                    {group.map(i => (
                      <div key={i.key} style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 9, padding: "11px 14px", marginBottom: 7, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1a2e1a" }}>{i.title} <StatusChip st={i.status} /></div>
                          <div style={{ fontSize: 10.5, color: "#60735f", marginTop: 2 }}>{typeLabel(i.type)}{i.season ? ` · ${i.season === "fall" ? "Fall" : "Spring"}` : ""}</div>
                          {i.dropboxPath && <div style={{ marginTop: 6 }}><span style={{ fontFamily: "ui-monospace, monospace", fontSize: 10.5, color: "#1a4a7a", background: "#eef4fb", border: "1px solid #d6e4f5", padding: "5px 9px", borderRadius: 6, display: "inline-block", wordBreak: "break-all" }}>🗂️ {i.dropboxPath}</span></div>}
                        </div>
                        {i.storagePath && <button onClick={(e) => openDoc(e, i.storagePath)} style={{ fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 6, border: "1px solid #c8d8c0", background: "#f4f8f0", color: "#3a6e32", cursor: "pointer", whiteSpace: "nowrap" }}>Open</button>}
                      </div>
                    ))}
                  </div>
                );
              })}
              {!detailItems.length && <div style={{ color: "#60735f", padding: "20px 0" }}>No documents on file.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FSA578Screen({ isMobile, farmers, mode = null, canUpload = false, canManage = false, restrictFarmerIds = null, viewerAll = false, can426 = false }) {
  // Portal mode = read-only viewer. Hides Upload/Box tabs, the internal "Mandy"
  // forwarding workflow, and all match/delete controls. Leaves Tracking + By-Farmer
  // browsing and View-PDF intact.
  // EXCEPTION 1: canUpload (limited agents) re-enables the Upload tab — but only for
  // their own customers (farmer match uses the RLS-scoped `farmers`, and the
  // farmer_578s INSERT RLS rejects any farmer outside their grants).
  // EXCEPTION 2: canManage (underwriter, e.g. Mandy) re-enables the "process
  // forwarded docs" controls — match-to-farmer + mark-status — via `manage`, which
  // the underwriter_update_578s RLS policy backs. It does NOT enable Upload/Box or
  // delete (those stay strictly !isPortal), and there's no underwriter DELETE policy.
  const isPortal = mode === "portal";
  const showUpload = !isPortal || canUpload;
  const manage = !isPortal || canManage;   // may match/mark 578s (not upload/delete)
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  // `records` stays [] when load578s throws — the catch only reached the console,
  // so a farmer whose documents failed to load was logged as a farmer with no
  // documents. The two are different to-dos.
  const [loadErr, setLoadErr] = useState(null);
  usePortalEmptyState("fsa578", records.length === 0, !loading, loadErr);
  const [tab, setTab] = useState(isPortal ? (viewerAll ? "pipeline" : "tracking") : "pipeline"); // "pipeline" | "library" | "upload" | "tracking" | "queue" | "documents" | "box"
  const [uploadState, setUploadState] = useState("idle"); // idle | reading | matching | confirming | uploading | done | error
  const [uploadFiles, setUploadFiles] = useState([]);
  const [currentFileIdx, setCurrentFileIdx] = useState(0);
  const [extractedData, setExtractedData] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [manualFarmerId, setManualFarmerId] = useState("");
  const [uploadError, setUploadError] = useState(null);
  const [uploadLog, setUploadLog] = useState([]);
  // SHA-256 of the file being processed (null = not computed). Stored on the row
  // as content_sha256 so the SAME bytes uploaded again are refused, not re-saved.
  const [fileSha, setFileSha] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedCounty, setSelectedCounty] = useState("all");
  const [trackQ, setTrackQ] = useState("");
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [seasonOverride, setSeasonOverride] = useState(null); // null = derive from the document
  // ⚠ THE SEASON AND THE YEAR COME FROM THE DOCUMENT'S OWN PROGRAM YEAR.
  //
  // This used to be `month >= 5 && month <= 8 ? spring : fall` — the date the
  // agent happened to upload — beside a hardcoded `tax_year: 2026`. Together
  // those filed every 578 uploaded between 1 September and 30 April as
  // `fall/2026`, a combination NO TAB DISPLAYS, so it vanished from the board.
  //
  // Kevin, 2026-09-25: "a 578 arriving this fall will be CY2027 wheat (or
  // triticale or barley)" — so the program year is the campaign. The manual
  // extraction now asks for it, exactly as the Box path always has.
  // `seasonOverride` stays: a derived default should never be a cage.
  const docYear = Number(extractedData?.cropYear) || null;
  // `basis` is carried, not discarded: the rule answers from the document's own
  // program year where the two live campaigns differ, and falls back to the
  // arrival month where they collapse (January to July, when spring and fall are
  // the same crop year). The badge says WHICH happened — a derived season that
  // cannot explain itself is what the old "(auto — Sep–Apr)" label was.
  const derived = CAMPAIGN578.seasonForProgramYear(docYear);
  const derivedSeason = derived.season;
  const derivedBasis = derived.basis;
  const effectiveSeason = seasonOverride || derivedSeason;
  // The year we will file under. The document's own is preferred; with none
  // readable we fall back to the live campaign for the season on screen, which
  // is at least a year somebody can see and correct — never a frozen literal.
  const effectiveTaxYear = docYear
    || CAMPAIGN578.liveCampaigns()[effectiveSeason === "fall" ? "fall" : "spring"];
  const [alreadySentToMandy, setAlreadySentToMandy] = useState(false);

  useEffect(() => { load578s(); }, []);

  async function load578s() {
    setLoading(true); setLoadErr(null);
    try {
      const rows = await sbFetch("farmer_578s");
      // Admin preview scopes to the previewed user's farmers (admin RLS sees all).
      const rset = restrictFarmerIds ? new Set(restrictFarmerIds) : null;
      const scoped = rset ? (rows || []).filter(r => rset.has(r.farmer_id)) : (rows || []);
      setRecords(scoped.sort((a, b) => new Date(b.received_at) - new Date(a.received_at)));
    } catch (e) {
      console.error("Failed to load 578s:", e);
      setLoadErr(String(e.message || e));
    }
    setLoading(false);
  }

  const addLog = (msg, type = "info") => {
    const ts = new Date().toLocaleTimeString("en-US", { hour12: false });
    setUploadLog(prev => [...prev, { ts, msg, type }]);
  };

  // Farmer name normalization for matching
  function normalizeName(name) {
    if (!name) return "";
    let s = name
      .toUpperCase()
      .replace(/[.,]/g, "")
      .replace(/\b(LLC|INC|INCORPORATED|CORP|CORPORATION|CO|LP|LLP|LLLP|TRUST|FARM|FARMS|FARMING)\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
    // Collapse consecutive single-letter words so "R L JEFFRES" matches
    // "R.L. JEFFRES" (which strips to "RL JEFFRES"). Mirrors box.js.
    let prev;
    do { prev = s; s = s.replace(/\b([A-Z])\s+(?=[A-Z]\b)/g, "$1"); } while (s !== prev);
    return s;
  }

  // Only farmers with real Supabase UUIDs can be matched. The PDF Import flow
  // pushes synthetic-id staging farmers (id = Date.now() + Math.random()) into
  // the in-memory farmers list when a doc has no policy number to merge on;
  // matching against those breaks the farmer_578s insert with a uuid syntax error.
  const isValidFarmerId = (id) =>
    typeof id === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

  function matchFarmer(extractedName) {
    if (!extractedName || !farmers?.length) return null;
    const norm = normalizeName(extractedName);
    if (!norm) return null;

    let bestMatch = null;
    let bestScore = 0;

    for (const f of farmers) {
      if (!isValidFarmerId(f.id)) continue;
      const fNorm = normalizeName(f.name);
      if (!fNorm) continue;

      // Exact match after normalization
      if (fNorm === norm) return { farmer: f, confidence: 100, method: "exact" };

      // Contains match
      if (fNorm.includes(norm) || norm.includes(fNorm)) {
        const score = 90;
        if (score > bestScore) { bestScore = score; bestMatch = { farmer: f, confidence: score, method: "contains" }; }
        continue;
      }

      // Word overlap scoring
      const normWords = norm.split(" ").filter(w => w.length > 1);
      const fWords = fNorm.split(" ").filter(w => w.length > 1);
      const matches = normWords.filter(w => fWords.includes(w)).length;
      const score = Math.round((matches / Math.max(normWords.length, fWords.length)) * 100);
      if (score > bestScore && score >= 50) {
        bestScore = score;
        bestMatch = { farmer: f, confidence: score, method: "word_overlap" };
      }
    }

    return bestMatch;
  }

  // Extract producer name from 578 PDF using AI
  async function extractFromPdf(file) {
    addLog(`Reading ${file.name}...`, "info");
    return new Promise((resolve, reject) => {
      (async () => {
        // Render page 1 ONLY to a JPEG and send that, instead of the whole PDF.
        // Large multi-tract producer prints exceed the Document API's
        // 100-page / 32MB cap AND Vercel's ~4.5MB function-body limit — either
        // one makes the proxy return an error body with no `content`, which
        // surfaced as a misleading "Unexpected end of JSON input". The prompt
        // only needs page 1, so a single rendered page sidesteps both limits.
        let imageData;
        try {
          const pdfjs = await getPDFJS();
          const arrayBuffer = await file.arrayBuffer();
          // isEvalSupported: false — CVE-2024-4367. See pdfToImages for the why.
          // This path renders a grower-supplied 578, so it is the likeliest one to
          // meet a hostile PDF. Never drop the flag.
          const pdf = await pdfjs.getDocument({ data: arrayBuffer, isEvalSupported: false }).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 2.0 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width; canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          imageData = canvas.toDataURL("image/jpeg", 0.85).split(",")[1];
        } catch (err) {
          addLog(`Failed to render PDF page 1: ${err.message}`, "error");
          reject(err);
          return;
        }
        addLog("Sending to AI for extraction...", "info");
        const reqBody = JSON.stringify({
              model: "claude-sonnet-4-6",
              max_tokens: 1000,
              messages: [{
                role: "user",
                content: [
                  { type: "image", source: { type: "base64", media_type: "image/jpeg", data: imageData } },
                  { type: "text", text: `This is page 1 of an FSA-578 (Report of Commodities) document.

Extract EXACTLY these four fields:
1. Producer Name — appears near the top left under "Producer Name and Address" (e.g. "ZELIFF FARMS LLC", "MT POCKETS FARMING LLC")
2. County — appears in the header area, the state/county where the producer is registered (e.g. "Niagara", "Livingston")
3. Is Nationwide — look for the text "(Nationwide Producer Print)" vs "(Producer Print)" near the top of the page. If it says "Nationwide Producer Print", return true. If it just says "Producer Print" (without Nationwide), return false.
4. Crop Year (also called "Program Year", "FSA Crop Year", or just "Year") — the integer year this 578 reports on. Usually near the top of the form.

Respond with ONLY a JSON object, no markdown, no backticks:
{"producerName": "EXACT NAME FROM DOCUMENT", "county": "COUNTY NAME", "isNationwide": true, "cropYear": 2027}

If you cannot find a field, use null for that field. cropYear must be an integer or null — never a string.` }
                ]
              }]
            });
        // Retry transient Anthropic failures (529 "Overloaded", 429 rate
        // limit, 5xx) with exponential backoff instead of erroring out on the
        // first hit — these are capacity blips, not bad input. Non-retryable
        // errors (and the final attempt) surface the real message.
        const MAX_ATTEMPTS = 5;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          try {
            const response = await adminApiFetch("/api/extract", { method: "POST", body: reqBody });
            const data = await response.json();
            if (!response.ok || data.error) {
              const msg = data.error?.message || `HTTP ${response.status}`;
              const retryable = response.status === 429 || response.status >= 500 || /overload/i.test(msg);
              if (retryable && attempt < MAX_ATTEMPTS) {
                const wait = 2000 * Math.pow(2, attempt - 1);
                addLog(`Anthropic ${msg} — retrying in ${Math.round(wait / 1000)}s (attempt ${attempt + 1}/${MAX_ATTEMPTS})...`, "info");
                await new Promise(r => setTimeout(r, wait));
                continue;
              }
              throw new Error(`Anthropic API error: ${msg}`);
            }
            const text = (data.content || []).map(c => c.text || "").join("");
            if (!text.trim()) {
              throw new Error(`Empty AI response (stop_reason: ${data.stop_reason || "unknown"})`);
            }
            // Extract JSON by slicing first { to last } — opus 4.8 can't take an
            // assistant prefill, and the model may wrap the object in prose.
            const clean = text.replace(/```json|```/g, "").trim();
            const first = clean.indexOf("{");
            const last = clean.lastIndexOf("}");
            const jsonStr = first >= 0 && last > first ? clean.slice(first, last + 1) : clean;
            const parsed = JSON.parse(jsonStr);
            addLog(`Extracted: "${parsed.producerName}" — ${parsed.county} County${parsed.isNationwide ? " (Nationwide)" : ""}`, "success");
            resolve(parsed);
            return;
          } catch (err) {
            addLog(`AI extraction failed: ${err.message}`, "error");
            reject(err);
            return;
          }
        }
      })();
    });
  }

  // Upload PDF to Supabase Storage
  async function uploadToStorage(file, farmerId) {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const storagePath = `578s/${farmerId}/${timestamp}_${safeName}`;
    addLog(`Uploading to storage: ${storagePath}`, "info");
    await sbStorageUpload("farmer-documents", storagePath, file);
    addLog("Upload complete", "success");
    return storagePath;
  }

  // SHA-256 hex of the file's bytes, or null where the browser cannot compute it
  // (crypto.subtle needs a secure context). Null means "no fingerprint" — the
  // upload proceeds exactly as it did before the fingerprint existed.
  async function sha256OfFile(file) {
    try {
      if (!window.crypto?.subtle) return null;
      const buf = await window.crypto.subtle.digest("SHA-256", await file.arrayBuffer());
      return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
    } catch { return null; }
  }

  // Is a 578 with these exact bytes already on file? Row or null. Until sql/097
  // adds content_sha256 the lookup fails on the missing column and answers null —
  // today's behaviour. Any other failure also answers null (logged): a lookup blip
  // must not stop a real 578 from being filed.
  async function findSameContent578(sha) {
    const filter = INTAKE578.contentHashFilter(sha);
    if (!filter) return null;
    try {
      // Direct read rather than sbFetch: sbFetch throws only "Supabase error: 400"
      // and drops the body, which is what says the column is missing (42703).
      const res = await authedFetch(`${SUPABASE_URL}/rest/v1/farmer_578s?select=id,farmer_id,extracted_name,received_at,original_filename&${filter}&limit=1`);
      if (!res.ok) throw new Error(`Supabase ${res.status}: ${(await res.text().catch(() => "")).slice(0, 300)}`);
      const rows = await res.json();
      return (Array.isArray(rows) && rows[0]) || null;
    } catch (e) {
      if (!INTAKE578.isMissingHashColumn(e)) addLog(`Duplicate check skipped: ${e.message}`, "warn");
      return null;
    }
  }

  // Process a single file
  // `batch` = { files, idx } when the caller has the list in hand. The first call
  // runs in the same tick as setUploadFiles, so this closure's uploadFiles is
  // still the OLD list — a duplicate that auto-advances must not read it.
  async function processFile(file, batch = null) {
    setUploadState("reading");
    setExtractedData(null);
    setMatchResult(null);
    setUploadError(null);
    setManualFarmerId("");
    setFileSha(null);

    try {
      // ⚠ THE SAME BYTES ARE THE SAME 578. Nothing used to check this path, so one
      // PDF uploaded twice became two rows — and a duplicate row is what makes the
      // acreage audit read every acre at 2.00x. Checked BEFORE extraction so a
      // repeat costs no AI call. A revised 578 is a different PDF and still flows.
      const sha = await sha256OfFile(file);
      setFileSha(sha);
      const dup = await findSameContent578(sha);
      if (dup) {
        const who = (farmers || []).find(f => f.id === dup.farmer_id)?.name;
        const msg = INTAKE578.duplicateMessage(dup, who);
        addLog(`${file.name}: ${msg}`, "warn");
        setUploadError(msg);
        setUploadState("duplicate");
        const list = batch?.files || uploadFiles;
        const nextIdx = (batch ? batch.idx : list.indexOf(file)) + 1;
        if (nextIdx > 0 && nextIdx < list.length) {
          setTimeout(() => { setCurrentFileIdx(nextIdx); processFile(list[nextIdx], { files: list, idx: nextIdx }); }, 2500);
        }
        return;
      }
      const extracted = await extractFromPdf(file);
      setExtractedData(extracted);
      setUploadState("matching");
      const match = matchFarmer(extracted?.producerName);
      setMatchResult(match);

      if (match && match.confidence >= 80) {
        addLog(`Matched: ${match.farmer.name} (${match.confidence}% confidence, ${match.method})`, "success");
      } else if (match) {
        addLog(`Low confidence match: ${match.farmer.name} (${match.confidence}%) — please confirm`, "warn");
      } else {
        addLog("No match found — select farmer manually", "warn");
      }
      setUploadState("confirming");
    } catch (err) {
      setUploadError(err.message);
      setUploadState("error");
    }
  }

  // Confirm match and save
  async function confirmAndSave(farmerId, file) {
    setUploadState("uploading");
    try {
      const storagePath = await uploadToStorage(file, farmerId);
      const row = {
        farmer_id: farmerId,
        county: extractedData?.county || null,
        tax_year: effectiveTaxYear,
        season: effectiveSeason,
        is_nationwide: extractedData?.isNationwide || false,
        source: "manual",
        storage_path: storagePath,
        forwarded_to_mandy: alreadySentToMandy,
        forwarded_at: alreadySentToMandy ? new Date().toISOString() : null,
        matched_by: matchResult?.confidence >= 80 ? "ai" : "manual",
        extracted_name: extractedData?.producerName || null,
        original_filename: file.name,
        status: alreadySentToMandy ? "forwarded" : "matched",
        ...(INTAKE578.isSha256Hex(fileSha) ? { content_sha256: fileSha } : {}),
        // Queued for its one read (sql/104) — the Box cron reads it off the request path.
        ...INTAKE578.arrivalLinesState(),
      };
      let inserted;
      try {
        inserted = await sbInsert("farmer_578s", [row]);
      } catch (e) {
        // content_sha256 (sql/097) and lines_status (sql/104) arrive separately; until
        // then save without them.
        if (!INTAKE578.isMissingArrivalColumn(e)) throw e;
        inserted = await sbInsert("farmer_578s", [INTAKE578.withoutArrivalColumns(row)]);
      }
      const insertedRow = Array.isArray(inserted) ? inserted[0] : null;
      addLog(`Saved 578 for farmer ${farmerId.slice(0, 8)} (${effectiveSeason})`, "success");

      // Auto-email Mandy unless the user explicitly checked "already sent".
      if (!alreadySentToMandy && insertedRow?.id) {
        addLog("Emailing Mandy with PDF attached...", "info");
        try {
          const r = await fetch(`/api/box?action=email-forward&id=${insertedRow.id}`, {
            method: "POST",
            headers: getApiAuthHeaders(),
          });
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
          if (data.sent) {
            const d = new Date(data.forwardedAt);
            const ts = `${d.toLocaleDateString()} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
            addLog(`✓ Forwarded to Mandy on ${ts}`, "success");
          } else if (data.alreadyForwarded) {
            const d = data.forwardedAt ? new Date(data.forwardedAt) : null;
            const ts = d ? `${d.toLocaleDateString()} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "earlier";
            addLog(`Already forwarded to Mandy on ${ts}`, "info");
          } else {
            addLog(`Email did not send: ${data.error || "unknown"}`, "warn");
          }
        } catch (e) {
          addLog(`Auto-forward to Mandy failed: ${e.message}. Row saved; mark forwarded manually in Tracking tab.`, "warn");
        }
      }
      setUploadState("done");

      const nextIdx = currentFileIdx + 1;
      if (nextIdx < uploadFiles.length) {
        setTimeout(() => { setCurrentFileIdx(nextIdx); processFile(uploadFiles[nextIdx]); }, 1500);
      } else {
        setTimeout(() => { load578s(); setUploadState("idle"); setUploadFiles([]); setCurrentFileIdx(0); setUploadLog([]); }, 2000);
      }
    } catch (err) {
      setUploadError(err.message);
      setUploadState("error");
      addLog(`Save failed: ${err.message}`, "error");
    }
  }

  // Mark as password protected
  async function markPasswordProtected(file) {
    try {
      // ⚠ `season` IS SUPPLIED HERE DELIBERATELY. This path used to omit it and
      // lean on the column DEFAULT ('spring'), which sql/076 removes — so a
      // locked fall 578 would have landed with a NULL season and appeared under
      // no tab at all. Every farmer_578s insert states both campaign columns;
      // check-campaign-578 asserts that over the whole file, not just this one.
      await sbInsert("farmer_578s", [{ farmer_id: null, county: null, tax_year: effectiveTaxYear,
        season: effectiveSeason, source: "manual",
        storage_path: null, forwarded_to_mandy: false, matched_by: null, extracted_name: null,
        original_filename: file.name, status: "password_protected",
        notes: "Password protected — needs manual unlock and re-upload" }]);
      addLog(`Flagged ${file.name} as password protected`, "warn");
      const nextIdx = currentFileIdx + 1;
      if (nextIdx < uploadFiles.length) { setCurrentFileIdx(nextIdx); processFile(uploadFiles[nextIdx]); }
      else { load578s(); setUploadState("idle"); setUploadFiles([]); setCurrentFileIdx(0); }
    } catch (err) { addLog(`Failed to flag: ${err.message}`, "error"); }
  }

  // View PDF (signed URL)
  async function viewPdf(storagePath) {
    try {
      const url = await sbStorageSignedUrl("farmer-documents", storagePath);
      trackPortalAction("open_document", { screen: "fsa578", kind: "578_pdf" });
      window.open(url, "_blank");
    } catch (err) { alert("Failed to get PDF URL: " + err.message); }
  }

  // Delete 578 record
  async function delete578(id) {
    if (!window.confirm("Delete this 578 record?")) return;
    try { await sbDelete("farmer_578s", `id=eq.${id}`); setRecords(prev => prev.filter(r => r.id !== id)); }
    catch (err) { alert("Delete failed: " + err.message); }
  }

  // Mark forwarded to Mandy
  async function markForwarded(id) {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/farmer_578s?id=eq.${id}`, {
        method: "PATCH", headers: { ...getAuthHeaders(), Prefer: "return=minimal" },
        body: JSON.stringify({ forwarded_to_mandy: true, forwarded_at: new Date().toISOString(), status: "forwarded" }),
      });
      if (!res.ok) throw new Error(`Patch failed: ${res.status}`);
      setRecords(prev => prev.map(r => r.id === id ? { ...r, forwarded_to_mandy: true, forwarded_at: new Date().toISOString(), status: "forwarded" } : r));
    } catch (err) { alert("Failed to update: " + err.message); }
  }

  // Manually link an unmatched 578 to a farmer. The auto-matcher misses
  // near-miss names (e.g. document "WALTON WAY" vs farmer "WALTONS WAY"), and
  // those records otherwise dead-end with no way to resolve them. Advances
  // status to forwarded if it was already sent to Mandy, else matched.
  async function matchToFarmer(id, farmerId) {
    if (!farmerId) return;
    try {
      const rec = records.find(r => r.id === id);
      const nextStatus = rec?.forwarded_to_mandy ? "forwarded" : "matched";
      const res = await fetch(`${SUPABASE_URL}/rest/v1/farmer_578s?id=eq.${id}`, {
        method: "PATCH", headers: { ...getAuthHeaders(), Prefer: "return=minimal" },
        body: JSON.stringify({ farmer_id: farmerId, matched_by: "manual", status: nextStatus }),
      });
      if (!res.ok) throw new Error(`Match failed: ${res.status}`);
      setRecords(prev => prev.map(r => r.id === id ? { ...r, farmer_id: farmerId, matched_by: "manual", status: nextStatus } : r));
    } catch (err) { alert("Failed to match: " + err.message); }
  }

  // Drag and drop handlers
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type === "application/pdf");
    if (files.length === 0) { alert("Please drop PDF files only"); return; }
    setUploadFiles(files); setCurrentFileIdx(0); setUploadLog([]); processFile(files[0], { files, idx: 0 });
  };
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type === "application/pdf");
    if (files.length === 0) return;
    setUploadFiles(files); setCurrentFileIdx(0); setUploadLog([]); processFile(files[0], { files, idx: 0 });
  };

  const farmerMap = useMemo(() => {
    const m = {}; (farmers || []).forEach(f => { if (f.id) m[f.id] = f; }); return m;
  }, [farmers]);

  const uniqueFarmers = useMemo(() => {
    const seen = new Set();
    return (farmers || []).filter(f => {
      if (!isValidFarmerId(f.id)) return false;
      if (!f.name || seen.has(f.name)) return false;
      seen.add(f.name);
      return true;
    }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [farmers]);

  const filteredRecords = useMemo(() => {
    let r = records;
    if (filterStatus !== "all") r = r.filter(x => x.status === filterStatus);
    if (selectedCounty !== "all") r = r.filter(x => (x.county || "").toLowerCase() === selectedCounty.toLowerCase());
    const s = trackQ.trim().toLowerCase();
    if (s) r = r.filter(x => `${farmerMap[x.farmer_id]?.name || ""} ${x.extracted_name || ""} ${x.county || ""} ${x.original_filename || ""}`.toLowerCase().includes(s));
    return r;
  }, [records, filterStatus, selectedCounty, trackQ, farmerMap]);

  const counties = useMemo(() => [...new Set(records.map(r => r.county).filter(Boolean))].sort(), [records]);

  // Tracking-table column layout. Portal viewers get a trimmed set (no internal
  // Source / Mandy-forwarding / Status columns and no row actions but View).
  const trackCols = isMobile
    ? (manage ? "1fr 80px 70px" : "1fr 60px")
    : (manage ? "1fr 120px 70px 100px 80px 100px 80px 70px" : "1fr 120px 70px 100px 70px");

  // Counts derive from farmer_id (the source of truth), not the status string.
  // Box-sourced unmatched rows carry status='pending_match', which slipped past
  // a status==='pending' filter and left Matched/Pending inconsistent with the
  // "Unmatched" list (which keys off farmer_id).
  const stats = useMemo(() => ({
    total: records.length,
    matched: records.filter(r => r.farmer_id).length,
    forwarded: records.filter(r => r.forwarded_to_mandy).length,
    pending: records.filter(r => !r.farmer_id && r.status !== "password_protected").length,
    passwordProtected: records.filter(r => r.status === "password_protected").length,
  }), [records]);

  const T578 = (active) => ({
    padding: "10px 20px", border: "none", borderBottom: active ? "3px solid #2d4a2d" : "3px solid transparent",
    background: active ? "#eef6ec" : "transparent", color: active ? "#1a2e1a" : "#60735f",
    fontWeight: active ? 800 : 500, fontSize: 13, cursor: "pointer", letterSpacing: "0.04em",
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          {/* A book-wide 578 holder (Daniele, Mandy) works the same queue we do, so
              they get the same page name — "578 Documents" vs "File Management" was
              two names for one screen and made every conversation about it ambiguous.
              A plain farmer still sees the farmer-facing wording for their own files. */}
          <h2 style={{ ...uiPageTitle(isMobile) }}>{(isPortal && !viewerAll) ? "FSA 578 Documents" : "File Management"}</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#60735f" }}>{(isPortal && !viewerAll) ? "View and download submitted FSA-578 acreage reports" : "578s, acreage reports & schedules of insurance — stored and e-signed by farmer"}</p>
        </div>
        <button onClick={load578s} style={{ padding: "8px 16px", border: "1px solid #c8d8c0", borderRadius: 7, fontSize: 12, background: "#f4f8f0", color: "#3a6e32", fontWeight: 700, cursor: "pointer" }}>↺ Refresh</button>
      </div>

      {/* Stats — portal viewers see document counts only, not the internal
          forwarding/pending/locked workflow state.

          ⚠ THESE COUNT 578s AND NOTHING ELSE, AND THEY DID NOT SAY SO. `stats` is
          computed entirely over `records` (farmer_578s); it does not look at
          farmer_documents at all. So the strip read "272 TOTAL · 272 MATCHED · 262
          FORWARDED" directly above a Pipeline showing "0 FARMERS" for Fall 2027 —
          two true numbers about two different things, stacked, with nothing to say
          they were not the same thing. Kevin: "Its a little ambiguous as to what tab
          should I be looking at." Naming the subject costs a word and removes the
          ambiguity; no count changed. */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {(isPortal ? [
          { label: "Documents", val: stats.total, color: "#1a2e1a", bg: "#eef6ec" },
          { label: "Counties", val: counties.length, color: "#2d6a1a", bg: "#e8f4e2" },
        ] : [
          { label: "578s on file", val: stats.total, color: "#1a2e1a", bg: "#eef6ec" },
          { label: "578s matched", val: stats.matched, color: "#2d6a1a", bg: "#e8f4e2" },
          { label: "578s to Mandy", val: stats.forwarded, color: "#1a5a2a", bg: "#e0f0e4" },
          { label: "578s unmatched", val: stats.pending, color: "#8a6a00", bg: "#fdf6e0" },
          { label: "578s locked", val: stats.passwordProtected, color: "#8a3a00", bg: "#fdf0e0" },
        ]).map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}22`, borderRadius: 8, padding: "10px 16px", minWidth: 80, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: s.color, letterSpacing: "0.08em", textTransform: "uppercase" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs — Upload + Box are admin-only; portal viewers get Tracking + By Farmer.
          ⚠ THIS ROW IS WHY THE PAGE DRAGGED SIDEWAYS ON A PHONE, and the last two tabs
          were the ones off the edge — which is to say **the FSA-426 tab itself**.
          Measured on production 2026-09-24 at a 390px viewport: six buttons, 498px of
          content in a 346px row, `flexWrap: nowrap`, Box ending at 448px and FSA-426 at
          520px. The app's main content area is `overflow-x: auto`, so instead of being
          clipped the whole page became draggable — headings sliding off to the left,
          which is exactly the screen Kevin sent. Wrapping costs a second row on a phone
          and changes nothing on a desktop, where all six already fit. */}
      {/* ⚠ THE TABS ARE IN WORKFLOW ORDER AND THEY SAY THEIR STEP NUMBER.
          Kevin, 2026-10-01: *"Its a little ambiguous as to what tab should I be looking
          at, what is the flow, what is the order of operations."*

          Before this the row read Pipeline · Library · Upload 578 · Tracking · Documents ·
          Box · FSA-426, which is very nearly the REVERSE of the real sequence: FSA-426 —
          the form that REQUESTS the 578s every other tab tracks — sat last, and the
          Pipeline you cannot start until a 578 arrives sat first. Nothing in the repo
          declared an order at all; the closest thing was a four-step sketch in a comment
          that contradicts the actual stage ladder.

          So the steps are numbered where the agent reads them, and the connectors are
          grouped after, unnumbered, because they are plumbing rather than a step you take
          per farmer. No tab was removed and no data path changed — this is the same set,
          in the order the work actually happens. */}
      {/* THE ORDER OF OPERATIONS, STATED ONCE, WHERE IT IS READ FIRST. Nothing in this
          repo declared one — the nearest thing was a four-step sketch inside a comment
          that contradicts the real stage ladder — so every reader had to infer the flow
          from a tab row that was very nearly its reverse. Agent-only: a customer does not
          run this process. */}
      {!isPortal && (
        <div style={{ fontSize: 12, color: "#4a6e4a", background: "#f4f8f2", border: "1px solid #dde8d8",
          borderRadius: 8, padding: "8px 12px", marginBottom: 12, lineHeight: 1.6 }}>
          <b style={{ color: "#1a2e1a" }}>How a document moves:</b>{" "}
          <b>1</b> ask FSA for the 578 → <b>2</b> it arrives (here or on the Box sync) →{" "}
          <b>3</b> work the acreage report in the Pipeline → <b>4</b> find anything later in the Library.
          <div style={{ fontSize: 11.5, color: "#6a7a6a" }}>
            Inside the Pipeline the stages run 1 missing 578 → 2 awaiting signature → 3 signed →
            4 ready for Silas (4a needs fill) → 5 Silas filled → 6 done, keyed by Mandy.
            The two 🔌 tabs are connections, not steps.
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid #dde8d8", marginBottom: 20 }}>
        {/* 1 REQUEST — the FSA-426 asks the county office for the 578s. Everything else
            here is downstream of it, so it goes first. */}
        {(!isPortal || can426) && <button style={T578(tab === "fsa426")} onClick={() => setTab("fsa426")}>1 · 🏛️ Request (FSA-426)</button>}
        {/* 2 INTAKE — the 578 arrives, by hand here or on the Box cron. */}
        {showUpload && <button style={T578(tab === "upload")} onClick={() => setTab("upload")}>{isPortal ? "" : "2 · "}⬆️ Upload 578</button>}
        {/* 3 WORK — the acreage report: fill, sign, file, key. */}
        {(!isPortal || viewerAll) && <button style={T578(tab === "pipeline")} onClick={() => setTab("pipeline")}>{isPortal ? "" : "3 · "}📊 Pipeline</button>}
        {/* 4 FIND — the cabinet of everything, once the work is done. */}
        {(!isPortal || viewerAll) && <button style={T578(tab === "library")} onClick={() => setTab("library")}>{isPortal ? "" : "4 · "}🗂️ Library</button>}
        <button style={T578(tab === "tracking")} onClick={() => setTab("tracking")}>📊 {isPortal ? "Documents" : "578 list"}</button>
        {isPortal && <button style={T578(tab === "queue")} onClick={() => setTab("queue")}>📑 By Farmer</button>}
        {/* ⚠ THIS TAB HAD NO BUTTON AND THE PANEL BEHIND IT WAS THE ONLY WAY TO REACH
            FOUR CONTROLS. `DocumentHubPanel` renders under `tab === "documents"` below —
            and nothing in the entire file called `setTab("documents")`, so Dropbox
            connect, "📥 Pull now", the watch-folder picker, the upload dropzone and the
            Dropbox Sign embedded-draft send were all unreachable from the running app.
            The tell was an error message pointing at one of them: AcreageFillButton
            tells the user "In File Management, click Pull from Dropbox now" — a control
            that had been renamed AND stranded. Agent-only, because it holds the agency's
            Dropbox connection and the e-sign send, and it sits beside Box because both
            are document connectors. The row is `flexWrap: "wrap"`, so a seventh button
            costs a second row on a phone and nothing on a desktop — see the note above,
            where an unwrapped row of six is what made the whole page drag sideways. */}
        {/* ⚠ RENAMED FROM "Documents", WHICH COLLIDED WITH A DIFFERENT TAB. The portal
            labels the 578 list "Documents" (two lines above), so the agent app had two
            tabs called Documents showing two different tables — and this one is not a
            document list at all: it is the Dropbox connection, the watch folder, the
            pull, and the Dropbox Sign e-sign send. Naming it for what it does removes
            the collision and puts it with the other connector. */}
        {!isPortal && <button style={T578(tab === "documents")} onClick={() => setTab("documents")}>🔌 Dropbox &amp; e-sign</button>}
        {!isPortal && <button style={T578(tab === "box")} onClick={() => setTab("box")}>🔌 📦 Box</button>}
      </div>

      {/* ═══════════════════ DOCUMENTS TAB (doc hub + Dropbox Sign e-sign) ═══════════════════ */}
      {tab === "pipeline" && (!isPortal || viewerAll) && <PipelineBoard farmers={farmers} readOnly={isPortal} />}

      {tab === "library" && (!isPortal || viewerAll) && <DocumentLibrary farmers={farmers} readOnly={isPortal} />}

      {tab === "documents" && !isPortal && <DocumentHubPanel farmers={farmers} />}

      {/* ═══════════════════ BOX TAB ═══════════════════ */}
      {tab === "box" && !isPortal && <BoxConnectionPanel onSyncComplete={load578s} />}
      {tab === "fsa426" && (!isPortal || can426) && <Fsa426Panel />}

      {/* ═══════════════════ UPLOAD TAB ═══════════════════ */}
      {tab === "upload" && showUpload && (
        <div>
          {/* Upload settings — always visible when idle */}
          {uploadState === "idle" && (
            <div>
              <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                {/* Season — date-based with override */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#4a6e4a" }}>Season:</div>
                  <span style={{ fontSize: 12, fontWeight: 800, padding: "5px 14px", borderRadius: 6,
                    background: effectiveSeason === "fall" ? "#6b4f00" : "#2d4a2d",
                    color: "#fff" }}>
                    {effectiveSeason === "fall" ? "🌾 Fall" : "🌱 Spring"}
                  </span>
                  {!seasonOverride && (
                    <span style={{ fontSize: 10, color: "#60735f" }}>
                      {derivedBasis === "program_year"
                        ? `(from the document — CY${docYear})`
                        : docYear
                          ? `(CY${docYear} is both campaigns right now — filed by arrival date)`
                          : "(no crop year read — filed by arrival date)"}
                    </span>
                  )}
                  {seasonOverride && (
                    <button onClick={() => setSeasonOverride(null)}
                      style={{ fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 4, border: "1px solid #c8d8c0", background: "#fff", color: "#60735f", cursor: "pointer" }}>
                      Reset to auto
                    </button>
                  )}
                  <button onClick={() => setSeasonOverride(effectiveSeason === "fall" ? "spring" : "fall")}
                    style={{ fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 4, border: "1px solid #c8d8c0", background: "#fff", color: "#4a6e4a", cursor: "pointer" }}>
                    Switch to {effectiveSeason === "fall" ? "Spring" : "Fall"}
                  </button>
                </div>

                {/* Skip auto-forward to Mandy */}
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#4a6e4a" }}
                  title="On save, the PDF is auto-emailed to Mandy. Check this to skip the email (e.g. she already has it).">
                  <input type="checkbox" checked={alreadySentToMandy} onChange={e => setAlreadySentToMandy(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: "#2d4a2d", cursor: "pointer" }} />
                  Skip email to Mandy (already sent)
                </label>
              </div>

              {/* Drop zone */}
              <div ref={dropRef} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{ border: `3px dashed ${dragOver ? "#2d6a1a" : "#c8d8c0"}`, borderRadius: 14,
                  padding: isMobile ? "40px 20px" : "60px 40px", textAlign: "center", cursor: "pointer",
                  background: dragOver ? "#e8f4e2" : "#fafcf8", transition: "all 0.2s" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1a2e1a", marginBottom: 6 }}>Drop 578 PDFs here</div>
                <div style={{ fontSize: 13, color: "#60735f" }}>or click to browse · supports multiple files</div>
                <input ref={fileInputRef} type="file" accept=".pdf" multiple onChange={handleFileSelect} style={{ display: "none" }} />
              </div>
            </div>
          )}

          {uploadState !== "idle" && uploadFiles.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 12, overflow: "hidden" }}>
              {/* Progress header */}
              <div style={{ background: "#f4f8f0", padding: "14px 20px", borderBottom: "1px solid #dde8d8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a" }}>
                  Processing: {uploadFiles[currentFileIdx]?.name}
                  {uploadFiles.length > 1 && <span style={{ color: "#60735f", fontWeight: 500 }}> ({currentFileIdx + 1} of {uploadFiles.length})</span>}
                </div>
                <div style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12, fontWeight: 700,
                  background: uploadState === "done" ? "#e0f0e4" : uploadState === "error" ? "#fdecea" : "#e8f0fa",
                  color: uploadState === "done" ? "#1a5a2a" : uploadState === "error" ? "#c0392b" : "#1a4a7a" }}>
                  {uploadState === "reading" && "Reading PDF..."}
                  {uploadState === "matching" && "Matching farmer..."}
                  {uploadState === "confirming" && "Confirm match"}
                  {uploadState === "uploading" && "Uploading..."}
                  {uploadState === "done" && "✓ Complete"}
                  {uploadState === "error" && "Error"}
                  {uploadState === "duplicate" && "Already on file"}
                </div>
              </div>

              {/* Confirm match */}
              {uploadState === "confirming" && (
                <div style={{ padding: 20 }}>
                  {/* Extracted info */}
                  <div style={{ background: "#f4f8f0", border: "1px solid #dde8d8", borderRadius: 8, padding: "14px 18px", marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#4a6e4a", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>AI Extracted from Page 1</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a" }}>{extractedData?.producerName || "—"}</div>
                      {extractedData?.isNationwide && (
                        <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 4, background: "#1a4a7a", color: "#fff" }}>NATIONWIDE</span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 4,
                        background: effectiveSeason === "fall" ? "#6b4f00" : "#2d4a2d", color: "#fff" }}>
                        {effectiveSeason === "fall" ? "🌾 FALL" : "🌱 SPRING"}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#60735f", marginTop: 2 }}>
                      {extractedData?.county ? `${extractedData.county} County` : "County not detected"}
                      {extractedData?.isNationwide === false && <span style={{ color: "#8a6a00", fontWeight: 600 }}> · Single county print</span>}
                    </div>
                  </div>

                  {/* High confidence match */}
                  {matchResult && matchResult.confidence >= 50 && (
                    <div style={{ background: matchResult.confidence >= 80 ? "#e8f4e2" : "#fdf6e0",
                      border: `1px solid ${matchResult.confidence >= 80 ? "#2d6a1a33" : "#8a6a0033"}`,
                      borderRadius: 8, padding: "14px 18px", marginBottom: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase",
                          color: matchResult.confidence >= 80 ? "#2d6a1a" : "#8a6a00" }}>
                          {matchResult.confidence >= 80 ? "✓ Match Found" : "⚠ Low Confidence Match"}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                          background: matchResult.confidence >= 80 ? "#2d6a1a" : "#8a6a00", color: "#fff" }}>
                          {matchResult.confidence}%
                        </span>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a" }}>{matchResult.farmer.name}</div>
                      <div style={{ fontSize: 12, color: "#60735f", marginTop: 2 }}>
                        {matchResult.farmer.policyNumber} · {matchResult.farmer.insurer || ""}
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                        <button onClick={() => confirmAndSave(matchResult.farmer.id, uploadFiles[currentFileIdx])}
                          style={{ flex: 1, padding: "10px", border: "none", borderRadius: 7, background: "#2d4a2d", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
                          ✓ Confirm & Save
                        </button>
                        <button onClick={() => setMatchResult(null)}
                          style={{ padding: "10px 16px", border: "1px solid #dde5dd", borderRadius: 7, background: "#fff", color: "#4a6e4a", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                          Wrong farmer
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Manual selection fallback */}
                  {(!matchResult || matchResult.confidence < 50) && (
                    <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 8, padding: "14px 18px", marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "#4a6e4a", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Select Farmer Manually</div>
                      <select value={manualFarmerId} onChange={e => setManualFarmerId(e.target.value)}
                        style={{ width: "100%", padding: "10px 12px", border: "1px solid #c8d8c0", borderRadius: 7, fontSize: 13, marginBottom: 10, background: "#fff", boxSizing: "border-box" }}>
                        <option value="">— Select farmer —</option>
                        {uniqueFarmers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                      <button onClick={() => manualFarmerId && confirmAndSave(manualFarmerId, uploadFiles[currentFileIdx])}
                        disabled={!manualFarmerId}
                        style={{ width: "100%", padding: "10px", border: "none", borderRadius: 7,
                          background: manualFarmerId ? "#2d4a2d" : "#ccc", color: "#fff", fontWeight: 800, fontSize: 13,
                          cursor: manualFarmerId ? "pointer" : "not-allowed" }}>
                        Save with Selected Farmer
                      </button>
                    </div>
                  )}

                  {/* Password-protected flag writes a null-farmer row — admin-only
                      (limited agents can only insert 578s tied to their own farmers). */}
                  {!isPortal && <button onClick={() => markPasswordProtected(uploadFiles[currentFileIdx])}
                    style={{ width: "100%", padding: "10px", border: "1px solid #e0a0a0", borderRadius: 7, background: "#fff", color: "#c0392b", fontWeight: 700, fontSize: 12, cursor: "pointer", marginTop: 4 }}>
                    🔒 Flag as Password Protected
                  </button>}
                </div>
              )}

              {uploadState === "uploading" && (
                <div style={{ padding: "40px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                  <div style={{ fontSize: 14, color: "#1a2e1a", fontWeight: 600 }}>Uploading to storage...</div>
                </div>
              )}

              {uploadState === "done" && (
                <div style={{ padding: "40px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                  <div style={{ fontSize: 14, color: "#2d6a1a", fontWeight: 700 }}>578 saved successfully</div>
                  {uploadFiles.length > 1 && currentFileIdx < uploadFiles.length - 1 && (
                    <div style={{ fontSize: 12, color: "#60735f", marginTop: 6 }}>Processing next file...</div>
                  )}
                </div>
              )}

              {uploadState === "duplicate" && (
                <div style={{ padding: 20 }}>
                  <div style={{ background: "#fdf6e0", border: "1px solid #e0c060", borderRadius: 8, padding: "14px 18px" }}>
                    <div style={{ fontSize: 13, color: "#6a4a00", fontWeight: 600 }}>{uploadError}</div>
                  </div>
                  {!(uploadFiles.length > 1 && currentFileIdx < uploadFiles.length - 1) && (
                    <button onClick={() => { load578s(); setUploadState("idle"); setUploadFiles([]); setCurrentFileIdx(0); setUploadLog([]); }}
                      style={{ marginTop: 12, padding: "10px 20px", border: "1px solid #dde5dd", borderRadius: 7, background: "#fff", color: "#4a6e4a", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                      Done
                    </button>
                  )}
                </div>
              )}

              {uploadState === "error" && (
                <div style={{ padding: 20 }}>
                  <div style={{ background: "#fdecea", border: "1px solid #e0a0a0", borderRadius: 8, padding: "14px 18px" }}>
                    <div style={{ fontSize: 13, color: "#c0392b", fontWeight: 600 }}>{uploadError}</div>
                  </div>
                  <button onClick={() => { setUploadState("idle"); setUploadFiles([]); setUploadLog([]); }}
                    style={{ marginTop: 12, padding: "10px 20px", border: "1px solid #dde5dd", borderRadius: 7, background: "#fff", color: "#4a6e4a", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    Try Again
                  </button>
                </div>
              )}

              {/* Log */}
              {uploadLog.length > 0 && (
                <div style={{ borderTop: "1px solid #dde8d8", padding: "12px 16px", maxHeight: 160, overflowY: "auto", background: "#fafcf8" }}>
                  {uploadLog.map((l, i) => (
                    <div key={i} style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", padding: "2px 0",
                      color: l.type === "error" ? "#c0392b" : l.type === "success" ? "#2d6a1a" : l.type === "warn" ? "#8a6a00" : "#60735f" }}>
                      <span style={{ color: "#aaa", marginRight: 8 }}>{l.ts}</span>{l.msg}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════ TRACKING TAB ═══════════════════ */}
      {tab === "tracking" && (
        <div>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <input value={trackQ} onChange={e => setTrackQ(e.target.value)} placeholder="🔍 Search by farmer or county…"
              style={{ flex: 1, minWidth: 200, padding: "8px 12px", border: "1px solid #c8d8c0", borderRadius: 7, fontSize: 13, background: "#fff" }} />
            {!isPortal && (
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ padding: "8px 12px", border: "1px solid #c8d8c0", borderRadius: 7, fontSize: 12, background: "#fff" }}>
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="matched">Matched</option>
                <option value="forwarded">Forwarded</option>
                <option value="password_protected">Password Protected</option>
              </select>
            )}
            <select value={selectedCounty} onChange={e => setSelectedCounty(e.target.value)}
              style={{ padding: "8px 12px", border: "1px solid #c8d8c0", borderRadius: 7, fontSize: 12, background: "#fff" }}>
              <option value="all">All Counties</option>
              {counties.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#60735f", fontSize: 15 }}>Loading 578 records…</div>
          ) : filteredRecords.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#60735f", fontSize: 15 }}>No 578 records found.</div>
          ) : (
            <div style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: trackCols, gap: 0,
                background: "#f4f8f0", borderBottom: "1px solid #dde8d8", padding: "9px 16px",
                fontSize: 10, fontWeight: 800, color: "#4a6e4a", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                <div>Farmer</div>
                {!isMobile && <><div>County</div><div>Season</div><div>Date</div>{!isPortal && <><div>Source</div><div>Mandy</div></>}</>}
                {!isPortal && <div>Status</div>}<div></div>
              </div>
              {filteredRecords.map((r, i) => {
                const farmer = farmerMap[r.farmer_id];
                const sc = { pending: { bg: "#fdf6e0", color: "#8a6a00", label: "Pending" },
                  pending_match: { bg: "#fdf0e8", color: "#a0561a", label: "Unmatched" },
                  matched: { bg: "#e8f4e2", color: "#2d6a1a", label: "Matched" },
                  forwarded: { bg: "#e0f0e4", color: "#1a5a2a", label: "Forwarded" },
                  password_protected: { bg: "#fdf0e0", color: "#8a3a00", label: "🔒 Locked" },
                }[r.status] || { bg: "#fdf6e0", color: "#8a6a00", label: "Pending" };
                return (
                  <div key={r.id} style={{ display: "grid", gridTemplateColumns: trackCols,
                    gap: 0, padding: "11px 16px", borderBottom: i < filteredRecords.length - 1 ? "1px solid #edf2e8" : "none",
                    background: i % 2 === 0 ? "#fff" : "#fafcf8", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a" }}>
                        {farmer?.name || r.extracted_name || <span style={{ color: "#c0392b", fontStyle: "italic" }}>Unmatched</span>}
                      </div>
                      {isMobile && <div style={{ fontSize: 10, color: "#60735f" }}>{r.county || "—"} · {r.season === "fall" ? "🌾" : "🌱"} · {new Date(r.received_at).toLocaleDateString()}</div>}
                      <div style={{ fontSize: 10, color: "#aaa", fontVariantNumeric: "tabular-nums" }}>{r.original_filename}</div>
                      {manage && !r.farmer_id && r.status !== "password_protected" && (
                        <select defaultValue="" onChange={(e) => { if (e.target.value) matchToFarmer(r.id, e.target.value); }}
                          style={{ marginTop: 4, fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #c8a060", background: "#fff", color: "#6b4f00", cursor: "pointer", maxWidth: 200 }}>
                          <option value="">🔗 Match to farmer…</option>
                          {uniqueFarmers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      )}
                    </div>
                    {!isMobile && (
                      <>
                        <div style={{ fontSize: 12, color: "#4a6e4a" }}>{r.county || "—"}</div>
                        <div><span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                          background: r.season === "fall" ? "#f4f0e8" : "#eef6ec",
                          color: r.season === "fall" ? "#6b4f00" : "#2d4a2d" }}>
                          {r.season === "fall" ? "🌾 Fall" : "🌱 Spring"}
                        </span></div>
                        <div style={{ fontSize: 12, color: "#60735f" }}>{new Date(r.received_at).toLocaleDateString()}</div>
                        {manage && (
                          <>
                            <div><span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
                              background: r.source === "email" ? "#e8f0fa" : r.source === "box" ? "#eef6ec" : "#f4f0e8",
                              color: r.source === "email" ? "#1a4a7a" : r.source === "box" ? "#2d6a1a" : "#6b4f00" }}>
                              {r.source === "email" ? "📧 Email" : r.source === "box" ? "📦 Box" : "⬆️ Manual"}
                            </span></div>
                            <div>
                              {r.forwarded_to_mandy ? (
                                <span style={{ fontSize: 11, color: "#2d6a1a", fontWeight: 700 }}>✓ Sent</span>
                              ) : r.status !== "password_protected" && r.farmer_id ? (
                                <button onClick={() => markForwarded(r.id)}
                                  style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4, border: "1px solid #c8d8c0", background: "#fff", color: "#3a6e32", cursor: "pointer" }}>
                                  Mark Sent
                                </button>
                              ) : <span style={{ fontSize: 11, color: "#aaa" }}>—</span>}
                            </div>
                          </>
                        )}
                      </>
                    )}
                    {manage && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: sc.bg, color: sc.color }}>{sc.label}</span>
                        {/* Revision flag — set by box.js when filename matches the
                            revision regex. Surfaces below the status badge so
                            it's visible at a glance during Mandy follow-up. */}
                        {r.notes && /^REVISION/i.test(r.notes) && (
                          <span title={r.notes} style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "#fff8e0", color: "#a06000", letterSpacing: "0.04em" }}>
                            🔄 REVISION
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {r.storage_path && (
                        <button onClick={() => viewPdf(r.storage_path)}
                          style={{ fontSize: 11, fontWeight: 700, padding: "4px 8px", borderRadius: 4, border: "1px solid #c8d8c0", background: "#fff", color: "#3a6e32", cursor: "pointer" }}>View</button>
                      )}
                      {/* The `r.season !== "fall"` gate that stood here is gone: the fill
                          is season-aware now. This row IS a 578, so it knows its own
                          campaign — pass it, rather than letting the server re-derive it
                          from the crop year, because the row is the better authority. */}
                      {!isPortal && r.farmer_id && r.storage_path && (
                        <AcreageFillButton farmerId={r.farmer_id} report578Path={r.storage_path} farmerName={farmer?.name || ""} cropYear={r.tax_year || null} fillSeason={r.season || null} label="🌾 Fill Acreage Report"
                          btnStyle={{ fontSize: 11, fontWeight: 700, padding: "4px 8px", borderRadius: 4, border: "1px solid #1f6b1a", background: "#eef7ea", color: "#1f6b1a", cursor: "pointer" }} />
                      )}
                      {!isPortal && (
                        <button onClick={() => delete578(r.id)}
                          style={{ fontSize: 11, padding: "4px 6px", borderRadius: 4, border: "1px solid #e0a0a0", background: "#fff", color: "#c0392b", cursor: "pointer" }}>×</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: "#60735f" }}>{filteredRecords.length} of {records.length} records shown</div>
        </div>
      )}

      {/* ═══════════════════ BY FARMER TAB ═══════════════════ */}
      {tab === "queue" && (
        <div>
          <p style={{ fontSize: 12, color: "#60735f", marginBottom: 16 }}>578s grouped by farmer — same view that appears on each farmer card.</p>
          {(() => {
            const grouped = {};
            records.filter(r => r.farmer_id).forEach(r => { if (!grouped[r.farmer_id]) grouped[r.farmer_id] = []; grouped[r.farmer_id].push(r); });
            const unmatched = records.filter(r => !r.farmer_id);
            const sortedFarmerIds = Object.keys(grouped).sort((a, b) => (farmerMap[a]?.name || "").localeCompare(farmerMap[b]?.name || ""));

            return (
              <div>
                {manage && unmatched.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#c0392b", marginBottom: 8 }}>⚠ Unmatched ({unmatched.length})</div>
                    {unmatched.map(r => (
                      <div key={r.id} style={{ background: "#fff8f0", border: "1px solid #e0c8a0", borderRadius: 8, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#1a2e1a" }}>{r.extracted_name || r.original_filename}</div>
                          <div style={{ fontSize: 10, color: "#60735f" }}>{r.status === "password_protected" ? "🔒 Password protected" : `Received ${new Date(r.received_at).toLocaleDateString()}`}</div>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          {r.storage_path && (
                            <button onClick={() => viewPdf(r.storage_path)}
                              style={{ fontSize: 11, fontWeight: 700, padding: "5px 9px", borderRadius: 5, border: "1px solid #c8d8c0", background: "#fff", color: "#3a6e32", cursor: "pointer" }}>View</button>
                          )}
                          {r.status !== "password_protected" && (
                            <select defaultValue="" onChange={(e) => { if (e.target.value) matchToFarmer(r.id, e.target.value); }}
                              style={{ fontSize: 11, padding: "5px 8px", borderRadius: 5, border: "1px solid #c8a060", background: "#fff", color: "#6b4f00", cursor: "pointer", maxWidth: 220 }}>
                              <option value="">🔗 Match to farmer…</option>
                              {uniqueFarmers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                          )}
                          {!isPortal && (
                            <button onClick={() => delete578(r.id)}
                              style={{ fontSize: 11, padding: "5px 8px", borderRadius: 5, border: "1px solid #e0a0a0", background: "#fff", color: "#c0392b", cursor: "pointer" }}>×</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {sortedFarmerIds.map(fid => {
                  const farmer = farmerMap[fid];
                  const docs = grouped[fid].sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
                  return (
                    <div key={fid} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#1a2e1a", marginBottom: 6, fontFamily: "'Lora',serif" }}>
                        {farmer?.name || docs[0]?.extracted_name || fid.slice(0, 8)}
                      </div>
                      {docs.map(r => (
                        <div key={r.id} style={{ background: "#fff", border: "1px solid #dde8d8", borderRadius: 8, padding: "10px 14px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#1a2e1a" }}>
                              {r.county ? `${r.county} County` : "Unknown County"} — received {new Date(r.received_at).toLocaleDateString()}
                            </div>
                            {!isPortal && (
                              <div style={{ fontSize: 10, color: "#60735f" }}>
                                {r.forwarded_to_mandy ? "✓ Forwarded to Mandy" : "Not yet forwarded"}
                                {r.matched_by && ` · Matched by ${r.matched_by}`}
                              </div>
                            )}
                          </div>
                          {r.storage_path && (
                            <button onClick={() => viewPdf(r.storage_path)}
                              style={{ fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 5, border: "1px solid #c8d8c0", background: "#f4f8f0", color: "#3a6e32", cursor: "pointer" }}>
                              View PDF
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
                {sortedFarmerIds.length === 0 && unmatched.length === 0 && (
                  <div style={{ textAlign: "center", padding: "60px 0", color: "#60735f", fontSize: 15 }}>No 578 records yet.</div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APP ENTRY POINT
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// LRP QUOTING VIEW — UBC-first daily quote board
// ─────────────────────────────────────────────────────────────────────────────
// Renders the latest sales-effective-date's lrp_daily_rates for an at-a-glance
// view of every (end_date × coverage_level) combo. Default card is UBC at
// target_weight 0.99 + 100% coverage (which matches ~80% of Sparta's book).
// Below: collapsible Feeder Cattle and Fed Cattle sections.
// ─────────────────────────────────────────────────────────────────────────────
// Feeder Cattle classes Clover Ag does not write — hide from quoter UI.
// We never sell these, so showing them just clutters the picker. RMA still
// publishes rates for them so they remain in lrp_daily_rates; we filter at
// display time rather than blocking the sync.
const HIDDEN_FEEDER_TYPE_LABELS = new Set([
  "Brahman Wt 1", "Brahman Wt 2",
  "Dairy Wt 1", "Dairy Wt 2",
  "Dairy Heifers Wt 1", "Dairy Heifers Wt 2",
  "Beef-on-dairy Wt 1", "Beef-on-dairy Wt 2",
]);
const isQuoterFeederLabel = (l) => l && l !== "UBC" && !HIDDEN_FEEDER_TYPE_LABELS.has(l);

function LRPQuotingView({ isMobile }) {
  const [rates, setRates]                 = useState([]);
  const [salesEffDate, setSalesEffDate]   = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [ubcTw, setUbcTw]                 = useState(0.99);
  const [extraCovs, setExtraCovs]         = useState({ "0.95": false, "0.90": false, "0.85": false });
  const [feederOpen, setFeederOpen]       = useState(false);
  const [fedOpen, setFedOpen]             = useState(false);
  const [cullOpen, setCullOpen]           = useState(false);
  // Per-section editable inputs. Initialized via useEffect after rates load.
  const [feederType, setFeederType]       = useState(null); // selected feeder type_label
  const [feederTw,   setFeederTw]         = useState(null);
  const [fedTw,      setFedTw]            = useState(null);
  const [cullTw,     setCullTw]           = useState(null);
  const [refreshing, setRefreshing]       = useState(false);
  const [refreshNote, setRefreshNote]     = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Latest distinct sales_eff_date
        const headers = getAuthHeaders();
        const distinctRes = await fetch(
          `${SUPABASE_URL}/rest/v1/lrp_daily_rates?select=sales_eff_date&order=sales_eff_date.desc&limit=200`,
          { headers }
        );
        const distinctData = distinctRes.ok ? await distinctRes.json() : [];
        const dates = [...new Set(distinctData.map(r => r.sales_eff_date))].sort().reverse();
        setAvailableDates(dates);
        if (!dates.length) { setRates([]); setLoading(false); return; }
        const latest = dates[0];
        setSalesEffDate(latest);
        await loadDate(latest);

        // Auto-refresh: RMA publishes today's rate file by 14:30 ET. After
        // that, if the latest sales_eff_date in our DB isn't today, kick the
        // LRP sync. Mon–Fri only (no RMA file on weekends).
        // Throttle to one attempt every 5 min per session — but ONLY mark as
        // "successfully synced today" after a non-zero upsert. A 0-row response
        // (RMA hasn't published yet, or transient blip) leaves the throttle in
        // place but doesn't lock out further attempts for the whole session.
        const now = new Date();
        const etOffsetHours = -4;  // EDT for current crop year
        const etHours = (now.getUTCHours() + etOffsetHours + 24) % 24;
        const etMinutes = now.getUTCMinutes();
        const pastPublishTime = etHours > 14 || (etHours === 14 && etMinutes >= 30);
        const todayUtcStr = now.toISOString().slice(0, 10);
        const isWeekday = now.getUTCDay() >= 1 && now.getUTCDay() <= 5;
        const successKey = `lrp-autosync-success-${todayUtcStr}`;
        const attemptKey = `lrp-autosync-attempt-${todayUtcStr}`;
        const alreadySucceeded = sessionStorage.getItem(successKey);
        const lastAttemptMs = parseInt(sessionStorage.getItem(attemptKey) || "0", 10);
        const fiveMinAgo = Date.now() - 5 * 60 * 1000;
        if (isWeekday && pastPublishTime && latest < todayUtcStr && !alreadySucceeded && lastAttemptMs < fiveMinAgo) {
          sessionStorage.setItem(attemptKey, String(Date.now()));
          try {
            const ymd = todayUtcStr.replace(/-/g, '');
            const syncRes = await fetch(`/api/rma-lrp-sync?ratesOnly=1&date=${ymd}`, { method: 'GET' });
            if (syncRes.ok) {
              const j = await syncRes.json();
              if ((j.rates_upserted ?? 0) > 0) {
                sessionStorage.setItem(successKey, '1');
                // Reload distinct dates + rates so the UI reflects today's file
                const dRes = await fetch(
                  `${SUPABASE_URL}/rest/v1/lrp_daily_rates?select=sales_eff_date&order=sales_eff_date.desc&limit=200`,
                  { headers }
                );
                const dData = dRes.ok ? await dRes.json() : [];
                const dList = [...new Set(dData.map(r => r.sales_eff_date))].sort().reverse();
                setAvailableDates(dList);
                if (dList.length) { setSalesEffDate(dList[0]); await loadDate(dList[0]); }
              }
            }
          } catch (syncErr) { console.warn("LRP auto-sync error:", syncErr); }
        }
      } catch (e) {
        console.warn("LRP rates load error:", e);
      }
      setLoading(false);
    })();
  }, []);

  async function loadDate(date) {
    const headers = getAuthHeaders();
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/lrp_daily_rates?sales_eff_date=eq.${date}&order=type_label.asc,end_date.asc,coverage_level.desc`,
      { headers }
    );
    const data = res.ok ? await res.json() : [];
    setRates(data);
  }

  async function changeDate(d) {
    setSalesEffDate(d);
    setLoading(true);
    await loadDate(d);
    setLoading(false);
  }

  // Trigger an on-demand RMA LRP rate sync (the same endpoint the daily M13
  // piggyback hits). Used when the freshness pill is yellow/red and Kevin
  // wants to refresh without waiting for tomorrow's cron.
  async function refreshRates() {
    setRefreshing(true); setRefreshNote(null);
    try {
      // ratesOnly=1 skips the slow endorsement-settle path so the request
      // finishes in ~5s instead of timing out at 60s. Settling is the cron's
      // job, not the Refresh Rates button's.
      const res = await fetch("/api/rma-lrp-sync?ratesOnly=1", { method: "GET" });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        setRefreshNote({ kind:"err", text:`Sync failed (HTTP ${res.status}): ${t.slice(0,200)}` });
      } else {
        const j = await res.json();
        if (j.success === false) setRefreshNote({ kind:"err", text:`Sync error: ${j.error || "unknown"}` });
        else if ((j.rates_upserted ?? 0) === 0) setRefreshNote({ kind:"warn", text: j.message || "Sync ran but upserted 0 rows." });
        else {
          setRefreshNote({ kind:"ok", text:`Synced ${j.rates_upserted} rate rows for ${j.ymd}.` });
          // Reload the latest available date
          const headers = getAuthHeaders();
          const distinctRes = await fetch(`${SUPABASE_URL}/rest/v1/lrp_daily_rates?select=sales_eff_date&order=sales_eff_date.desc&limit=200`, { headers });
          const distinctData = distinctRes.ok ? await distinctRes.json() : [];
          const dates = [...new Set(distinctData.map(r => r.sales_eff_date))].sort().reverse();
          setAvailableDates(dates);
          if (dates.length) { setSalesEffDate(dates[0]); await loadDate(dates[0]); }
        }
      }
    } catch (e) { setRefreshNote({ kind:"err", text:`Sync threw: ${e.message}` }); }
    setRefreshing(false);
  }

  // Compute freshness of the latest available rate file. Color-codes the pill.
  function freshnessFor(latestIso) {
    if (!latestIso) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const data  = new Date(latestIso + "T00:00:00");
    const days  = Math.round((today - data) / 86400000);
    let color, bg, text;
    if      (days <= 1) { color = "#1f6f3a"; bg = "#e6f4e8"; text = days === 0 ? "Synced today" : "Synced yesterday"; }
    else if (days <= 3) { color = "#8a5a00"; bg = "#fff4d6"; text = `${days} days old`; }
    else                { color = "#9a1f1f"; bg = "#fde0e0"; text = `${days} days old — sync may be broken`; }
    return { color, bg, text, days };
  }

  // Initialize editable target-weight + feeder-type defaults whenever the rate
  // set changes (date change, first load). Each type's default tw is the
  // midpoint of its [target_low_weight, target_high_weight] range.
  useEffect(() => {
    if (!rates.length) return;
    const fedSample  = rates.find(r => r.commodity === "Fed Cattle" && r.type_code === "820");
    const cullSample = rates.find(r => r.commodity === "Fed Cattle" && r.type_code === "821");
    const feederLabels = [...new Set(rates.filter(r => r.commodity === "Feeder Cattle" && isQuoterFeederLabel(r.type_label)).map(r => r.type_label))].sort();
    const midpoint = (s, fb) => s ? +(((Number(s.target_low_weight) + Number(s.target_high_weight)) / 2)).toFixed(1) : fb;
    setFedTw (prev => prev ?? midpoint(fedSample,  13));
    setCullTw(prev => prev ?? midpoint(cullSample, 12));
    if (!feederType && feederLabels.length) {
      const initial = feederLabels[0];
      const s = rates.find(r => r.type_label === initial);
      setFeederType(initial);
      setFeederTw(midpoint(s, 5));
    }
  }, [rates]);

  const fmt$  = (v) => v == null ? "—" : "$" + Number(v).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
  const fmt$0 = (v) => v == null ? "—" : "$" + Number(v).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
  const fmtDate = (s) => s ? new Date(s+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";

  // RMA LRP-Cattle subsidy schedule (verified 2026-04-29 against RMA's
  // Livestock Risk Protection program rules + against actual Producer Premium
  // values published by R&H's quoting tool for Steers Wt 1 at TW=5.5).
  // cost_per_cwt in lrp_daily_rates is GROSS (= coverage_price × rate); the
  // producer's actual cost is gross × this share. Producer share matches RMA
  // to within $1 per head due to whole-dollar rounding.
  //
  // If RMA changes the subsidy schedule, this needs updating — confirm against
  // the current LRP basic provisions / actuarial manual at rma.usda.gov.
  // Source schedule (post-2020 enhancement, current as of CY2026):
  //   Coverage Level   | Subsidy | Producer Share
  //   ≥ 95%            |  35%    |  0.65
  //   90.00 – 94.99%   |  40%    |  0.60
  //   85.00 – 89.99%   |  45%    |  0.55
  //   80.00 – 84.99%   |  50%    |  0.50
  //   < 80%            |  55%    |  0.45
  const lrpProducerShare = (covLevel) => {
    // ONE definition, in src/lrp/lrpPush.js. This was a hand-maintained copy
    // kept "in sync" with two others by a comment; the quoter, the Profit
    // Calculator, the alert email and the Push Signal must price one
    // endorsement identically or a customer sees two premiums for it.
    // The shared rule returns null when it cannot answer (unparseable level, or
    // a pre-2021 reinsurance year whose schedule we hold no source for); the
    // quoter degrades to the UNSUBSIDISED gross, which overstates the premium
    // and can only ever make a quote look worse, never better.
    const sh = sharedLrpProducerShare(covLevel);
    return sh ? sh.share : 1;
  };

  // Build rows for a given filter spec → { typeLabel, targetWeight }
  // Each output row corresponds to one end_date with all coverage levels stacked horizontally.
  function rowsFor(typeLabel, targetWeight) {
    const matched = rates.filter(r => {
      if (r.type_label !== typeLabel) return false;
      const tw = Number(targetWeight);
      const lo = Number(r.target_low_weight);
      const hi = Number(r.target_high_weight);
      if (Number.isFinite(lo) && tw < lo - 0.005) return false;
      if (Number.isFinite(hi) && tw > hi + 0.005) return false;
      return true;
    });
    // Group by end_date, collect coverage_level → row
    const byEnd = {};
    for (const r of matched) {
      const k = r.end_date;
      if (!byEnd[k]) byEnd[k] = { end_date: r.end_date, weeks: r.endorsement_length_weeks, byCov: {} };
      byEnd[k].byCov[Number(r.coverage_level).toFixed(2)] = r;
    }
    return Object.values(byEnd).sort((a,b) => a.end_date.localeCompare(b.end_date));
  }

  // Column set for a card — always include 100%, plus any user-toggled extras.
  function colsFor() {
    const out = ["1.00"];
    if (extraCovs["0.95"]) out.unshift("0.95");
    if (extraCovs["0.90"]) out.unshift("0.90");
    if (extraCovs["0.85"]) out.unshift("0.85");
    return out;
  }
  const covLabel = (c) => `${Math.round(parseFloat(c)*100)}%`;

  // Render one quote card (UBC, Steers Wt 1, etc.) — table of end_dates × coverage levels.
  // When `shareOpen` is toggled, the card swaps to a portrait, screenshot-ready layout
  // sized for ~390px viewports (single phone column) with logo, header, stacked end-date
  // "tickets" at 100% coverage, and a Clover Ag contact footer.
  function QuoteCard({ title, subtitle, typeLabel, targetWeight, accent="#2d4a2d" }) {
    const [shareOpen, setShareOpen] = useState(false);
    const rows = rowsFor(typeLabel, targetWeight);
    const cols = colsFor();
    if (!rows.length) return (
      <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 18px", color:"#60735f", fontSize:12, marginBottom:12 }}>
        No quotes available for {typeLabel} at target weight {targetWeight} cwt for this date.
      </div>
    );

    if (shareOpen) {
      const tw = Number(targetWeight);
      return (
        <div style={{ background:"#fff", border:`1px solid ${accent}33`, borderRadius:10, marginBottom:18, overflow:"hidden" }}>
          <div style={{ padding:"6px 12px", borderBottom:`1px solid ${accent}22`, background:`${accent}0c` }}>
            <button onClick={() => setShareOpen(false)}
              style={{ background:"none", border:"none", color:accent, fontSize:12, fontWeight:700, cursor:"pointer", padding:0 }}>
              ← Back to table
            </button>
          </div>
          <div style={{ width:"100%", maxWidth:380, margin:"0 auto", padding:"12px 10px 12px", boxSizing:"border-box" }}>
            <img src="/Clover_Ag_Logo1_transparent.png" alt="Clover Ag"
              style={{ width:"42%", maxWidth:130, display:"block", margin:"0 auto 6px" }} />
            <div style={{ textAlign:"center", marginBottom:10 }}>
              <div style={{ fontFamily:"'Lora','Georgia',serif", fontWeight:700, fontSize:15, color:"#1a2e1a", lineHeight:1.2 }}>{title}</div>
              <div style={{ fontSize:11, color:"#5a7a5a", marginTop:2 }}>{tw} cwt · 100% coverage</div>
              <div style={{ fontSize:10, color:"#677167", marginTop:1 }}>Sales Eff {fmtDate(salesEffDate)}</div>
            </div>
            {/* Compact one-line-per-end-date table — fixed-width flex columns
                so numeric values right-align cleanly and never get clipped.
                Padding/gaps tightened to fit on iPhone SE (320px viewport)
                with End Date column able to shrink (minWidth:0 below). */}
            <div style={{ border:`1px solid ${accent}22`, borderRadius:8, overflow:"hidden" }}>
              <div style={{ display:"flex", padding:"5px 6px", background:`${accent}0c`, fontSize:10, fontWeight:800, color:accent, textTransform:"uppercase", letterSpacing:"0.05em", gap:4 }}>
                <span style={{ flex:"1 1 auto", minWidth:0 }}>End Date</span>
                <span style={{ flex:"0 0 24px", textAlign:"center" }}>Wks</span>
                <span style={{ flex:"0 0 56px", textAlign:"right" }}>Coverage</span>
                <span style={{ flex:"0 0 44px", textAlign:"right" }}>Premium</span>
              </div>
              {rows.map((r,i) => {
                const cell = r.byCov["1.00"];
                if (!cell) return null;
                const cwt        = Number(cell.coverage_price);
                const perHeadCov = cwt * tw;
                const premCwt    = Number(cell.cost_per_cwt) || 0;
                // cost_per_cwt is GROSS; producer pays gross × subsidy share.
                const perHeadPrem= premCwt * tw * lrpProducerShare(cell.coverage_level);
                return (
                  <div key={r.end_date}
                    style={{ display:"flex", padding:"6px 6px", gap:4, alignItems:"baseline", background: i%2 ? "#fafcfa" : "#fff", borderTop: i===0 ? "none" : "1px solid #f0f4f0", fontSize:12 }}>
                    <span style={{ flex:"1 1 auto", minWidth:0, fontWeight:700, color:"#1a2e1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{fmtDate(r.end_date)}</span>
                    <span style={{ flex:"0 0 24px", textAlign:"center", fontVariantNumeric: "tabular-nums",  color:"#60735f", fontSize:11 }}>{r.weeks ?? "—"}</span>
                    <span style={{ flex:"0 0 56px", textAlign:"right", fontVariantNumeric: "tabular-nums",  fontWeight:800, color:accent }}>{fmt$0(perHeadCov)}</span>
                    <span style={{ flex:"0 0 44px", textAlign:"right", fontVariantNumeric: "tabular-nums",  color:"#7a4a00" }}>{fmt$0(perHeadPrem)}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop:8, fontSize:10, color:"#677167", textAlign:"center", lineHeight:1.4 }}>
              All values per head · <span style={{ fontWeight:700, color:"#2d4a2d" }}>Clover Ag</span> · cloverag.com · (585) 233-1066
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ background:"#fff", border:`1px solid ${accent}33`, borderRadius:10, marginBottom:18, overflow:"hidden" }}>
        <div style={{ padding: isMobile ? "10px 14px" : "12px 18px", background:`${accent}0c`, borderBottom:`1px solid ${accent}22`, display:"flex", alignItems:"baseline", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
          <div>
            <span style={{ fontWeight:800, fontSize:15, color:"#1a2e1a" }}>{title}</span>
            <span style={{ fontSize:12, color:"#60735f", marginLeft:10 }}>{subtitle}</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:11, color:"#677167", fontVariantNumeric: "tabular-nums" }}>{rows.length} end dates × {cols.length} coverage{cols.length === 1 ? "" : "s"}</span>
            <button onClick={() => setShareOpen(true)}
              style={{ padding:"4px 10px", border:`1px solid ${accent}33`, background:"#fff", borderRadius:6, fontSize:11, fontWeight:700, color:accent, cursor:"pointer", whiteSpace:"nowrap" }}>
              📱 Share
            </button>
          </div>
        </div>
        <div style={{ overflowX:"auto" }}>
          {/* On mobile with one coverage column we drop the table's minWidth so
              the Prem $/head column is visible without horizontal scroll. With
              extras toggled (multi-cov) the table still scrolls, since fitting
              5 numeric cols into ~360px isn't readable. */}
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:isMobile?11:12, minWidth: isMobile && cols.length === 1 ? 0 : (cols.length > 1 ? 720 : 540) }}>
            <thead>
              <tr style={{ background:"#fafcfa" }}>
                <th style={{ padding:isMobile?"6px 6px":"8px 14px", textAlign:"left", fontSize:isMobile?9:10, fontWeight:800, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em" }}>End Date</th>
                <th style={{ padding:isMobile?"6px 4px":"8px 14px", textAlign:"left", fontSize:isMobile?9:10, fontWeight:800, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em", width:isMobile?32:60 }}>Wks</th>
                {cols.map(c => (
                  <th key={c} colSpan={3} style={{ padding:isMobile?"6px 4px":"8px 14px", textAlign:"center", fontSize:isMobile?10:11, fontWeight:800, color:accent, borderLeft:"1px solid #f0f4f0" }}>{covLabel(c)} Coverage</th>
                ))}
              </tr>
              <tr style={{ background:"#fafcfa", borderBottom:"1px solid #e4ece4" }}>
                <th></th><th></th>
                {cols.flatMap(c => [
                  <th key={c+"_cov"}  style={{ padding:isMobile?"3px 4px 6px":"4px 8px 8px", textAlign:"right", fontSize:10, fontWeight:700, color:"#677167", textTransform:"uppercase", letterSpacing:"0.05em", borderLeft:"1px solid #f0f4f0" }}>Cov $/cwt</th>,
                  <th key={c+"_head"} style={{ padding:isMobile?"3px 4px 6px":"4px 8px 8px", textAlign:"right", fontSize:10, fontWeight:700, color:"#677167", textTransform:"uppercase", letterSpacing:"0.05em" }}>Cov $/head</th>,
                  <th key={c+"_prem"} style={{ padding:isMobile?"3px 4px 6px":"4px 8px 8px", textAlign:"right", fontSize:10, fontWeight:700, color:"#677167", textTransform:"uppercase", letterSpacing:"0.05em" }}>Prem $/head</th>,
                ])}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.end_date} style={{ borderTop:"1px solid #f0f4f0", background: i%2 ? "#fafcfa" : "#fff" }}>
                  <td style={{ padding:isMobile?"6px 6px":"7px 14px", fontWeight:700, color:"#1a2e1a", whiteSpace:"nowrap" }}>{fmtDate(r.end_date)}</td>
                  <td style={{ padding:isMobile?"6px 4px":"7px 14px", fontVariantNumeric: "tabular-nums",  color:"#5a7a5a" }}>{r.weeks ?? "—"}</td>
                  {cols.flatMap(c => {
                    const cell = r.byCov[c];
                    if (!cell) return [
                      <td key={c+"_cov_"+r.end_date}  colSpan={3} style={{ padding:"7px 14px", textAlign:"center", color:"#ccc", borderLeft:"1px solid #f0f4f0" }}>—</td>
                    ];
                    const tw = Number(targetWeight);
                    const cwt = Number(cell.coverage_price);
                    const perHeadCov = cwt * tw;
                    const premCwt = Number(cell.cost_per_cwt) || 0;
                    // cost_per_cwt is GROSS premium per cwt; producer pays gross × subsidy share.
                    const perHeadPrem = premCwt * tw * lrpProducerShare(cell.coverage_level);
                    return [
                      <td key={c+"_cov_"+r.end_date}  style={{ padding:isMobile?"6px 4px":"7px 8px", textAlign:"right", fontVariantNumeric: "tabular-nums",  fontWeight:700, color:"#1a2e1a", borderLeft:"1px solid #f0f4f0" }}>{fmt$(cwt)}</td>,
                      <td key={c+"_head_"+r.end_date} style={{ padding:isMobile?"6px 4px":"7px 8px", textAlign:"right", fontVariantNumeric: "tabular-nums",  fontWeight:800, color:accent }}>{fmt$0(perHeadCov)}</td>,
                      <td key={c+"_prem_"+r.end_date} style={{ padding:isMobile?"6px 4px":"7px 8px", textAlign:"right", fontVariantNumeric: "tabular-nums",  color:"#7a4a00" }}>{fmt$0(perHeadPrem)}</td>,
                    ];
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 60, textAlign:"center", color:"#60735f" }}>Loading LRP quotes...</div>;
  if (!salesEffDate) return (
    <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #d8e0d0" }}>
      <div style={{ fontSize:32, marginBottom:10 }}>💵</div>
      <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:6 }}>No LRP rate data yet</div>
      <div style={{ fontSize:13, color:"#60735f" }}>The first daily sync will populate quotes from RMA's daily file.</div>
    </Card>
  );

  // Distinct types — filter by type_code for Fed Cattle / Cull Cows split since
  // type_code is authoritative (820 = regular Fed Cattle, 821 = Cull Cows) and
  // resilient to label renames in the rate file.
  const feederTypeLabels = [...new Set(rates.filter(r => r.commodity === "Feeder Cattle" && isQuoterFeederLabel(r.type_label)).map(r => r.type_label))].sort();
  const fedSample        = rates.find(r => r.commodity === "Fed Cattle" && r.type_code === "820");
  const cullSample       = rates.find(r => r.commodity === "Fed Cattle" && r.type_code === "821");
  const feederSample     = feederType ? rates.find(r => r.type_label === feederType) : null;

  // Clamp helper: keeps tw inside the type's [low, high] range
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const twInputStyle = {
    width:78, padding:"6px 10px", borderRadius:6, border:"1px solid #d0e0d0",
    fontSize:13, fontVariantNumeric: "tabular-nums", textAlign:"right",
  };
  const selectStyle = {
    padding:"6px 10px", borderRadius:6, border:"1px solid #d0e0d0",
    fontSize:13, background:"#fff", flex:"1 1 auto", minWidth:160,
  };
  const inputRowStyle = {
    display:"flex", alignItems:"center", gap:isMobile?8:14, flexWrap:"wrap",
    padding:"10px 14px", background:"#fafcfa", borderBottom:"1px solid #f0f4f0",
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems: isMobile ? "stretch" : "center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10, flexDirection: isMobile ? "column" : "row" }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <div style={{ fontFamily:"'Lora','Georgia',serif", fontSize:isMobile?18:24, fontWeight:700, color:"#1a2e1a" }}>LRP Daily Quotes</div>
            {(() => {
              const f = freshnessFor(availableDates[0]);
              if (!f) return null;
              return (
                <span style={{ fontSize:11, fontWeight:700, color:f.color, background:f.bg, padding:"3px 8px", borderRadius:4, border:`1px solid ${f.color}33` }}>
                  🛰️ {f.text}
                </span>
              );
            })()}
          </div>
          <div style={{ fontSize:12, color:"#60735f", marginTop:2 }}>RMA-published rates · Sales Effective {fmtDate(salesEffDate)}</div>
          {refreshNote && (
            <div style={{ fontSize:11, marginTop:4, color: refreshNote.kind==="err" ? "#9a1f1f" : refreshNote.kind==="warn" ? "#8a5a00" : "#1f6f3a" }}>{refreshNote.text}</div>
          )}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          {isCurrentUserAdmin() && (<button onClick={refreshRates} disabled={refreshing}
            title="Pull the latest RMA LRP rate file now (same endpoint the daily M13 piggyback hits)"
            style={{ padding:"6px 12px", border:"1px solid #d0e0d0", background: refreshing ? "#f0f4f0" : "#fff", borderRadius:6, fontSize:11, fontWeight:700, color:"#2d4a2d", cursor: refreshing ? "wait" : "pointer", whiteSpace:"nowrap" }}>
            {refreshing ? "⏳ Syncing…" : "🔄 Refresh rates"}
          </button>)}
          <label style={{ fontSize:11, color:"#60735f", fontWeight:700 }}>Date:</label>
          <select value={salesEffDate} onChange={e => changeDate(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:12, background:"#fff", flex: isMobile ? "1 1 0" : "0 0 auto" }}>
            {availableDates.map(d => <option key={d} value={d}>{fmtDate(d)}</option>)}
          </select>
        </div>
      </div>

      {/* Controls */}
      <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"12px 14px", marginBottom:18, display:"flex", flexWrap:"wrap", gap: isMobile ? 12 : 16, alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:11, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em" }}>UBC TW</span>
          <input type="number" min="0.60" max="0.99" step="0.01" value={ubcTw}
            onChange={e => setUbcTw(parseFloat(e.target.value) || 0.99)}
            style={{ width:74, padding:"6px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:13, fontVariantNumeric: "tabular-nums", textAlign:"right" }}
          />
          <span style={{ fontSize:11, color:"#677167" }}>cwt</span>
        </div>
        {!isMobile && <div style={{ width:1, height:28, background:"#e0eae0" }}></div>}
        <div style={{ display:"flex", alignItems:"center", gap:isMobile?6:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:11, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em" }}>+ Cov:</span>
          {["0.95","0.90","0.85"].map(c => (
            <label key={c} style={{ display:"flex", alignItems:"center", gap:3, fontSize:12, color:"#1a2e1a", cursor:"pointer" }}>
              <input type="checkbox" checked={!!extraCovs[c]} onChange={e => setExtraCovs(prev => ({...prev, [c]:e.target.checked}))} />
              {Math.round(parseFloat(c)*100)}%
            </label>
          ))}
        </div>
      </div>

      {/* Default UBC card */}
      <QuoteCard
        title="🐄 UBC — Unborn Calves"
        subtitle={`Target weight ${ubcTw} cwt`}
        typeLabel="UBC"
        targetWeight={ubcTw}
        accent="#2d4a2d"
      />

      {/* Collapsible: Feeder Cattle — type dropdown + editable TW */}
      {feederTypeLabels.length > 0 && (
        <div style={{ marginBottom:18 }}>
          <button onClick={() => setFeederOpen(o => !o)}
            style={{ width:"100%", padding:"12px 18px", border:"1px solid #e0eae0", borderRadius:10, background:"#fff", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:14, fontWeight:700, color:"#1a2e1a" }}>
            <span>🐄 Feeder Cattle <span style={{ color:"#60735f", fontWeight:500, fontSize:12, marginLeft:6 }}>{feederTypeLabels.length} weight class{feederTypeLabels.length === 1 ? "" : "es"}</span></span>
            <span style={{ color:"#60735f" }}>{feederOpen ? "▼" : "▶"}</span>
          </button>
          {feederOpen && feederType && (
            <div style={{ marginTop:10 }}>
              <div style={{ background:"#fff", border:"1px solid #1a4a8a22", borderTopLeftRadius:10, borderTopRightRadius:10 }}>
                <div style={inputRowStyle}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flex:"1 1 auto", minWidth:200 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em" }}>Type</span>
                    <select value={feederType}
                      onChange={e => {
                        const next = e.target.value;
                        setFeederType(next);
                        const s = rates.find(r => r.type_label === next);
                        if (s) setFeederTw(+(((Number(s.target_low_weight) + Number(s.target_high_weight)) / 2)).toFixed(1));
                      }}
                      style={selectStyle}>
                      {feederTypeLabels.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em" }}>TW</span>
                    <input type="number"
                      min={feederSample ? Number(feederSample.target_low_weight)  : 1}
                      max={feederSample ? Number(feederSample.target_high_weight) : 10}
                      step="0.5" value={feederTw ?? ""}
                      onChange={e => {
                        const v = parseFloat(e.target.value);
                        if (!Number.isFinite(v) || !feederSample) { setFeederTw(v); return; }
                        setFeederTw(clamp(v, Number(feederSample.target_low_weight), Number(feederSample.target_high_weight)));
                      }}
                      style={twInputStyle}
                    />
                    <span style={{ fontSize:11, color:"#677167" }}>cwt</span>
                    {feederSample && (
                      <span style={{ fontSize:10, color:"#677167", fontVariantNumeric: "tabular-nums" }}>[{Number(feederSample.target_low_weight)}–{Number(feederSample.target_high_weight)}]</span>
                    )}
                  </div>
                </div>
              </div>
              <QuoteCard
                title={feederType}
                subtitle={`Target weight ${feederTw} cwt`}
                typeLabel={feederType}
                targetWeight={feederTw || 5}
                accent="#1a4a8a"
              />
            </div>
          )}
        </div>
      )}

      {/* Collapsible: Fed Cattle (regular beef, 13–52 wk endorsements) — editable TW */}
      {fedSample && (
        <div style={{ marginBottom:18 }}>
          <button onClick={() => setFedOpen(o => !o)}
            style={{ width:"100%", padding:"12px 18px", border:"1px solid #e0eae0", borderRadius:10, background:"#fff", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:14, fontWeight:700, color:"#1a2e1a" }}>
            <span>🐂 Fed Cattle <span style={{ color:"#60735f", fontWeight:500, fontSize:12, marginLeft:6 }}>regular beef · 13–52 wk endorsements</span></span>
            <span style={{ color:"#60735f" }}>{fedOpen ? "▼" : "▶"}</span>
          </button>
          {fedOpen && (
            <div style={{ marginTop:10 }}>
              <div style={{ background:"#fff", border:"1px solid #7a3b1022", borderTopLeftRadius:10, borderTopRightRadius:10 }}>
                <div style={inputRowStyle}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em" }}>TW</span>
                    <input type="number"
                      min={Number(fedSample.target_low_weight)}
                      max={Number(fedSample.target_high_weight)}
                      step="0.5" value={fedTw ?? ""}
                      onChange={e => {
                        const v = parseFloat(e.target.value);
                        if (!Number.isFinite(v)) { setFedTw(v); return; }
                        setFedTw(clamp(v, Number(fedSample.target_low_weight), Number(fedSample.target_high_weight)));
                      }}
                      style={twInputStyle}
                    />
                    <span style={{ fontSize:11, color:"#677167" }}>cwt</span>
                    <span style={{ fontSize:10, color:"#677167", fontVariantNumeric: "tabular-nums" }}>[{Number(fedSample.target_low_weight)}–{Number(fedSample.target_high_weight)}]</span>
                  </div>
                </div>
              </div>
              <QuoteCard
                title="Fed Cattle"
                subtitle={`Target weight ${fedTw} cwt`}
                typeLabel="Fed Cattle"
                targetWeight={fedTw || 13}
                accent="#7a3b10"
              />
            </div>
          )}
        </div>
      )}

      {/* Collapsible: Cull Cows (Holstein-discounted, 13-week only) — editable TW */}
      {cullSample && (
        <div style={{ marginBottom:18 }}>
          <button onClick={() => setCullOpen(o => !o)}
            style={{ width:"100%", padding:"12px 18px", border:"1px solid #e0eae0", borderRadius:10, background:"#fff", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:14, fontWeight:700, color:"#1a2e1a" }}>
            <span>🐄 Cull Cows <span style={{ color:"#60735f", fontWeight:500, fontSize:12, marginLeft:6 }}>dairy Holstein · 13-wk endorsement only</span></span>
            <span style={{ color:"#60735f" }}>{cullOpen ? "▼" : "▶"}</span>
          </button>
          {cullOpen && (
            <div style={{ marginTop:10 }}>
              <div style={{ background:"#fff", border:"1px solid #a05a3022", borderTopLeftRadius:10, borderTopRightRadius:10 }}>
                <div style={inputRowStyle}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em" }}>TW</span>
                    <input type="number"
                      min={Number(cullSample.target_low_weight)}
                      max={Number(cullSample.target_high_weight)}
                      step="0.5" value={cullTw ?? ""}
                      onChange={e => {
                        const v = parseFloat(e.target.value);
                        if (!Number.isFinite(v)) { setCullTw(v); return; }
                        setCullTw(clamp(v, Number(cullSample.target_low_weight), Number(cullSample.target_high_weight)));
                      }}
                      style={twInputStyle}
                    />
                    <span style={{ fontSize:11, color:"#677167" }}>cwt</span>
                    <span style={{ fontSize:10, color:"#677167", fontVariantNumeric: "tabular-nums" }}>[{Number(cullSample.target_low_weight)}–{Number(cullSample.target_high_weight)}]</span>
                  </div>
                </div>
              </div>
              <QuoteCard
                title="Cull Cows"
                subtitle={`Target weight ${cullTw} cwt`}
                typeLabel="Cull Cows"
                targetWeight={cullTw || 12}
                accent="#a05a30"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LRP PROFIT CALCULATOR — cost of production + LRP floor → "can I insure a profit?"
//
// A cattle cost-of-gain tool with LRP coverage + producer premium baked in.
// Single scenario in, a profit-per-endorsement-end-date table out. The producer
// premium (gross cost_per_cwt × subsidy share) is folded in as a COST, so the
// profit shown is the GUARANTEED FLOOR profit: what you net if the market falls
// to or below your coverage price. Available in BOTH the farmer portal and the
// admin app (no farmer binding — works for prospects/non-customers too).
//
// Reuses lrp_daily_rates exactly as LRPQuotingView does:
//   coverage_price = insured floor $/cwt; cost_per_cwt = GROSS premium $/cwt
//   (producer pays gross × subsidy share for the coverage level).
// ─────────────────────────────────────────────────────────────────────────────
function LRPProfitView({ isMobile, mode = "admin", farmers = [] }) {
  const [rates, setRates]                   = useState([]);
  const [salesEffDate, setSalesEffDate]     = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading]               = useState(true);

  // Cattle selection
  const [species, setSpecies]     = useState("feeder"); // "feeder" | "fed"
  // Local cash minus the national index, $/cwt. ZERO by default and typed by
  // the user — the same shape the "My position" mode below already uses, so the
  // two halves of this calculator cannot disagree about basis.
  const [planBasis, setPlanBasis] = useState("0");
  const [feederType, setFeederType] = useState(null);    // selected feeder type_label
  const [covLevel, setCovLevel]   = useState("1.00");

  // Cost-of-production inputs (single scenario). Defaults reflect a typical
  // background-to-feeder lot; they flip to a finishing lot when species == fed.
  const [head, setHead]               = useState("50");
  const [buyWt, setBuyWt]             = useState("550");  // lbs/head in
  const [buyPrice, setBuyPrice]       = useState("2.40"); // $/lb purchase
  const [cogPerLb, setCogPerLb]       = useState("1.10"); // $/lb of gain
  const [deathLoss, setDeathLoss]     = useState("1.5");  // %
  const [otherPerHead, setOtherPerHead] = useState("0");  // $/head misc (vet, interest, yardage not in COG)
  const [adg, setAdg]                 = useState("2.50"); // rate of gain, lb/day — bridges days on feed ↔ sell weight

  // The LRP end date and the sell weight are two views of the same thing: longer
  // on feed = heavier cattle = different coverage. ADG ties them. Two pick modes:
  //   endDate  — choose an LRP end date; sell weight = buyWt + ADG × days on feed
  //   targetWt — enter a target weight; we find when they hit it and snap to the
  //              nearest LRP end date.
  const [buyDate, setBuyDate]               = useState(() => new Date().toISOString().slice(0, 10));
  const [pickMode, setPickMode]             = useState("endDate"); // "endDate" | "targetWt"
  const [endDateSel, setEndDateSel]         = useState("");        // chosen LRP end date (ISO)
  const [targetWt, setTargetWt]             = useState("850");     // target sell weight, lbs (targetWt mode)
  const [sellWtOverride, setSellWtOverride] = useState("");        // "" = use ADG-derived weight

  // ── "My position" mode ─────────────────────────────────────────────────────
  // For an owner who already bought the cattle AND holds LRP: the premium is a
  // SUNK cost (not re-quoted off today's grid) and the coverage price/end date
  // are FIXED (what they actually bought). Output is a P&L across ending market
  // prices — showing where the LRP floor kicks in — not a single "floor profit".
  // Only feeder/fed (target_weight >= 1 cwt) — UBC dairy-calf endorsements are
  // sold day-old and don't use a cost-of-gain tool.
  const [calcMode, setCalcMode]     = useState("purchase"); // "purchase" | "position"

  // ── Several purchase lots ──────────────────────────────────────────────────
  // Buying isn't one homogeneous draft — a load of 550s in March and a load of
  // 650s in April are one feeding period at different cost bases, and blending
  // them into one average buy weight hides which lot is underwater.
  //
  // Everything a lot SHARES (days on feed, rate of gain, cost of gain, death
  // loss, coverage level, end date) stays a single input; only head, in-weight
  // and buy price are per lot. Each lot then gets its own sell weight, and so
  // its own guarantee and premium off the same coverage price — LRP is quoted
  // in $/cwt, so a heavier lot insures more dollars per head from the same
  // endorsement. Blending is head-weighted, which reconciles exactly with the
  // per-lot rows because every term is linear in head.
  //
  // Off by default: one lot is the common case and the plain form reads faster.
  const [lotsOn, setLotsOn] = useState(false);
  const [lots, setLots] = useState([
    { head: "50", buyWt: "550", buyPrice: "2.40" },
    { head: "50", buyWt: "650", buyPrice: "2.25" },
  ]);
  const setLot = (i, patch) => setLots(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));
  const addLot = () => setLots(ls => [...ls, { head: "", buyWt: "", buyPrice: "" }]);
  const removeLot = (i) => setLots(ls => ls.length > 1 ? ls.filter((_, j) => j !== i) : ls);

  // Cost of gain built from an actual ration in Ag Calculators, if one was sent
  // over this session. Offered as a chip rather than applied silently — the
  // number on screen should only change because someone chose to change it.
  const rationCog = readRationCog();
  // NY feeder-steer auction prints (feeder_weekly, the Push Signal's own input
  // source). Offered as a one-click starting point for the buy price — never
  // applied — because $2.40/lb was a typed constant that had drifted ~$1.25/lb
  // under the market by 2026-10 (NY 5–6 cwt steers printed $3.65–3.70/lb).
  const [auctionRows, setAuctionRows] = useState([]);
  // ── SIMPLE vs DETAILED (2026-10-15) ───────────────────────────────────────
  // Kevin: "super user friendly for someone who doesn't know all of their numbers
  // and also super powerful for someone who wants a very intensive profit
  // calculator." ONE calculator, two depths, ONE set of math underneath:
  //   Simple   — head, weights, type, end date, coverage. Every other input is a
  //              TYPICAL number, filled in and labelled with where it came from:
  //              NY auction price, cost of gain from this month's feed prices,
  //              the Push Signal's STANDARD gain / death loss / ownership costs,
  //              and the measured NY basis where the animal is inside the
  //              population it was measured on (never outside it).
  //   Detailed — every field editable, plus costs beyond feed itemized line by
  //              line through the SAME engine rule (ownershipCostsBeyondFeed).
  // Simple is an explicit, visible choice — the card says "these are typical
  // numbers, not yours" — which is what keeps it honest; Detailed keeps the
  // September rule that nothing is applied for the producer.
  const [detailMode, setDetailMode] = useState("simple");   // "simple" | "detailed"
  const simple = detailMode === "simple" && calcMode === "purchase";
  const [itemize, setItemize] = useState(false);
  const [ownRates, setOwnRates] = useState(null);            // null = standard; else { key: string }
  const [feedPx, setFeedPx] = useState(null);                // dmc_margins row: the Push Signal's feed-price source
  useEffect(() => {
    let alive = true;
    sbFetch("dmc_margins", { select: "year,month,corn_price,alfalfa_hay,soybean_meal", order: "year.desc,month.desc", limit: 1 })
      .then(r => { if (alive && r && r[0]) setFeedPx(r[0]); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    let alive = true;
    const since = new Date(Date.now() - 70 * 86400000).toISOString().slice(0, 10);
    sbFetch("feeder_weekly", { select: "series,week_ending,avg_price,head_count",
      filters: ["region=eq.NY", "series=like.STEER_*", `week_ending=gte.${since}`], order: "week_ending.desc" })
      .then(r => { if (alive) setAuctionRows(r || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const [ends, setEnds]             = useState([]);           // feeder/fed endorsements (prefill source)
  const [selEndId, setSelEndId]     = useState("");           // "" = manual entry
  const [posCovPrice, setPosCovPrice] = useState("");         // $/cwt floor (actual)
  const [posPremHead, setPosPremHead] = useState("");         // producer premium $/head (sunk, actual)
  const [posSellWt, setPosSellWt]   = useState("850");        // sell weight lb (from target_weight)
  const [posBasis, setPosBasis]     = useState("0");          // local cash − national index, $/cwt (LRP doesn't cover basis)

  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

  // RMA LRP-Cattle subsidy share (same schedule as LRPQuotingView). Producer
  // pays gross premium × this share. Confirm against RMA actuarial manual if
  // RMA revises the subsidy schedule.
  const producerShare = (c) => {
    // Same shared rule as the quoter and the Push Signal — see lrpProducerShare
    // in LRPQuotingView. A third copy is how these two screens come to disagree
    // about what one producer pays.
    const sh = sharedLrpProducerShare(c);
    return sh ? sh.share : 1;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const headers = getAuthHeaders();
        const distinctRes = await fetch(
          `${SUPABASE_URL}/rest/v1/lrp_daily_rates?select=sales_eff_date&order=sales_eff_date.desc&limit=200`,
          { headers }
        );
        const distinctData = distinctRes.ok ? await distinctRes.json() : [];
        const dates = [...new Set(distinctData.map(r => r.sales_eff_date))].sort().reverse();
        setAvailableDates(dates);
        if (!dates.length) { setRates([]); setLoading(false); return; }
        setSalesEffDate(dates[0]);
        await loadDate(dates[0]);
      } catch (e) {
        console.warn("LRP profit rates load error:", e);
      }
      setLoading(false);
    })();
  }, []);

  async function loadDate(date) {
    const headers = getAuthHeaders();
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/lrp_daily_rates?sales_eff_date=eq.${date}&order=type_label.asc,end_date.asc,coverage_level.desc`,
      { headers }
    );
    setRates(res.ok ? await res.json() : []);
  }

  async function changeDate(d) {
    setSalesEffDate(d);
    setLoading(true);
    await loadDate(d);
    setLoading(false);
  }

  // Fetch feeder/fed endorsements — the prefill source for "My position" mode.
  // SECURITY (must not leak other farmers' data):
  //   1. RLS on lrp_endorsements scopes this per-user server-side — an admin gets
  //      the book (is_admin()), a portal farmer gets ONLY their own farms
  //      (farmers_read_own_lrp via farmer_portal_users). getAuthHeaders() sends
  //      the user's JWT, so the row filter actually applies.
  //   2. Belt-and-suspenders: for non-admins we ALSO filter client-side to the
  //      farmer_ids in the RLS-scoped `farmers` prop (see visibleEnds below), so
  //      two independent guards must both fail before anything cross-farmer shows.
  // UBC (target_weight < 1) excluded — dairy-calf endorsements don't use a
  // cost-of-gain P&L. farmer_id + name via the policies→farmers embed.
  // PORTAL ONLY: the admin app gets NO book-wide picker (manual entry instead).
  const isPortal = mode === "portal";
  useEffect(() => {
    if (!isPortal) return; // admin app: no book-wide customer list here
    (async () => {
      try {
        const headers = getAuthHeaders();
        const res = await fetch(
          `${SUPABASE_URL}/rest/v1/lrp_endorsements?select=id,commodity,coverage_price,end_date,effective_date,head,target_weight,producer_premium,actual_end_value,payable_indemnity,policies(farmer_id,farmers(name))&target_weight=gte.1&order=end_date.desc&limit=500`,
          { headers }
        );
        const data = res.ok ? await res.json() : [];
        setEnds((data || []).map(e => ({
          ...e,
          farmer_id: e.policies?.farmer_id || null,
          farmer: e.policies?.farmers?.name || "—",
        })));
      } catch (e) { console.warn("LRP endorsements load error:", e); }
    })();
  }, []);

  // The endorsements this user may prefill from — ALWAYS scoped to the farms in
  // the `farmers` prop, which the portal shell passes as the per-customer set
  // (real portal users are RLS-scoped; admin "view as" preview passes only the
  // previewed customer's farms). We do NOT bypass for admins: in preview the
  // admin session bypasses RLS and the fetch returns the whole book, so THIS
  // client filter is the guard that keeps the view showing only the one customer.
  const visibleEnds = useMemo(() => {
    const allowed = new Set();
    (farmers || []).forEach(f => {
      if (f.id) allowed.add(f.id);
      (f._allFarmerIds || []).forEach(id => id && allowed.add(id));
    });
    return ends.filter(e => e.farmer_id && allowed.has(e.farmer_id));
  }, [ends, farmers]);

  // Prefill the position inputs from the selected endorsement (actual terms).
  // Look up only within visibleEnds so an out-of-scope id can't prefill.
  useEffect(() => {
    if (!selEndId) return;
    const e = visibleEnds.find(x => String(x.id) === String(selEndId));
    if (!e) return;
    setSpecies(e.commodity === "Fed Cattle" ? "fed" : "feeder");
    if (e.coverage_price != null) setPosCovPrice(String(e.coverage_price));
    const hd = Number(e.head) || 0;
    if (e.producer_premium != null && hd > 0) setPosPremHead((Number(e.producer_premium) / hd).toFixed(2));
    if (hd > 0) setHead(String(hd));
    // target_weight is in cwt for feeder/fed (e.g. 8.5 = 850 lb)
    if (e.target_weight != null) setPosSellWt(String(Math.round(Number(e.target_weight) * 100)));
    if (e.effective_date) setBuyDate(e.effective_date);
  }, [selEndId, visibleEnds]);

  // Initialize feeder type once rates land — default to Steers Wt 2 (the
  // backgrounding/feeder class this book writes most), else first available.
  useEffect(() => {
    if (!rates.length || feederType) return;
    const labels = [...new Set(rates.filter(r => r.commodity === "Feeder Cattle" && isQuoterFeederLabel(r.type_label)).map(r => r.type_label))].sort();
    if (labels.length) setFeederType(labels.includes("Steers Wt 2") ? "Steers Wt 2" : labels[0]);
  }, [rates]);

  // Switch species → reset weights/prices/ADG to lot-appropriate defaults so the
  // sell weight lands inside the LRP target band for the new commodity. Clears
  // the end-date pick + weight override so the new scenario re-derives clean.
  function pickSpecies(s) {
    setSpecies(s);
    setEndDateSel(""); setSellWtOverride("");
    if (s === "fed") {
      setBuyWt("850"); setBuyPrice("2.30"); setCogPerLb("1.15"); setTargetWt("1400"); setAdg("3.25");
    } else {
      setBuyWt("550"); setBuyPrice("2.40"); setCogPerLb("1.10"); setTargetWt("850");  setAdg("2.50");
    }
  }

  const fmt$  = (v) => v == null || !Number.isFinite(v) ? "—" : (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
  const fmt$0 = (v) => v == null || !Number.isFinite(v) ? "—" : (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
  const fmtDate = (s) => s ? new Date(s+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";

  // Freshness pill (mirrors LRPQuotingView).
  function freshnessFor(latestIso) {
    if (!latestIso) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const data  = new Date(latestIso + "T00:00:00");
    const days  = Math.round((today - data) / 86400000);
    if (days <= 1) return { color:"#1f6f3a", bg:"#e6f4e8", text: days === 0 ? "Synced today" : "Synced yesterday" };
    if (days <= 3) return { color:"#8a5a00", bg:"#fff4d6", text: `${days} days old` };
    return { color:"#9a1f1f", bg:"#fde0e0", text: `${days} days old — sync may be broken` };
  }

  // ── LRP rows for the selected commodity/type + its end dates ────────────────
  const typeRows = rates.filter(r => {
    if (species === "fed") return r.commodity === "Fed Cattle" && r.type_code === "820";
    return r.type_label === feederType;
  });
  const sample = typeRows[0] || null;
  const band   = sample ? { lo: Number(sample.target_low_weight), hi: Number(sample.target_high_weight) } : null;
  const availableEndDates = [...new Set(typeRows.map(r => r.end_date))].sort();
  const weeksByEnd = {}; typeRows.forEach(r => { weeksByEnd[r.end_date] = r.endorsement_length_weeks; });

  const daysBetween = (aIso, bIso) => (aIso && bIso) ? Math.round((new Date(bIso+"T00:00:00") - new Date(aIso+"T00:00:00")) / 86400000) : null;
  const addDaysIso  = (iso, n) => { const d = new Date(iso+"T00:00:00"); d.setDate(d.getDate() + Math.round(n)); return d.toISOString().slice(0,10); };

  // ── Resolve the scenario: end date ↔ sell weight (ADG bridges them) ─────────
  const adgN = num(adg);
  let resolvedEndDate = null, daysOnFeed = null, readyDate = null, targetDays = null;
  if (pickMode === "endDate") {
    resolvedEndDate = (endDateSel && availableEndDates.includes(endDateSel)) ? endDateSel : (availableEndDates[0] || null);
    daysOnFeed = resolvedEndDate ? daysBetween(buyDate, resolvedEndDate) : null;
  } else {
    targetDays = adgN > 0 ? (num(targetWt) - num(buyWt)) / adgN : null;
    readyDate  = (targetDays != null && targetDays >= 0) ? addDaysIso(buyDate, targetDays) : null;
    if (readyDate && availableEndDates.length) {
      resolvedEndDate = availableEndDates.reduce((bestD, d) =>
        bestD == null || Math.abs(daysBetween(readyDate, d)) < Math.abs(daysBetween(readyDate, bestD)) ? d : bestD, null);
    } else {
      resolvedEndDate = availableEndDates[0] || null;
    }
    daysOnFeed = resolvedEndDate ? daysBetween(buyDate, resolvedEndDate) : null;
  }

  // Derived sell weight. End-date mode: weight at the End Date from ADG. Target
  // mode: the target itself. Either is overridable.
  const derivedSellWt = pickMode === "targetWt"
    ? num(targetWt)
    : Math.max(num(buyWt), num(buyWt) + adgN * (daysOnFeed || 0));
  const sellWt  = sellWtOverride !== "" ? num(sellWtOverride) : Math.round(derivedSellWt);
  const sellCwt = sellWt / 100;

  // ── Cost of production (per head) ──────────────────────────────────────────
  const headN   = Math.max(0, Math.round(num(head)));
  const gainLbs = Math.max(0, sellWt - num(buyWt));
  const purchasePerHead = num(buyWt) * num(buyPrice);
  const gainPerHead     = gainLbs * num(cogPerLb);
  const placedPerHead   = purchasePerHead + gainPerHead + num(otherPerHead);
  const dl              = Math.min(0.5, Math.max(0, num(deathLoss) / 100));
  // Death loss spreads the cost of dead animals over the survivors.
  const costPerSoldHead = dl < 1 ? placedPerHead / (1 - dl) : placedPerHead;
  const deathLossPerHead = costPerSoldHead - placedPerHead;
  const costPerSoldCwt  = sellCwt > 0 ? costPerSoldHead / sellCwt : 0;

  const inBand = band ? (sellCwt >= band.lo - 0.005 && sellCwt <= band.hi + 0.005) : false;

  // ── The single chosen LRP endorsement ──────────────────────────────────────
  const cell = (resolvedEndDate && inBand)
    ? typeRows.find(r => r.end_date === resolvedEndDate && Number(r.coverage_level).toFixed(2) === covLevel)
    : null;
  const result = cell ? (() => {
    const coveragePrice = Number(cell.coverage_price);
    const guaranteeHead = coveragePrice * sellCwt;
    const premHead      = (Number(cell.cost_per_cwt) || 0) * sellCwt * producerShare(cell.coverage_level);
    const totalCostHead = costPerSoldHead + premHead;
    const profitHead    = guaranteeHead - totalCostHead;
    return { coveragePrice, guaranteeHead, premHead, totalCostHead, profitHead,
             profitCwt: sellCwt > 0 ? profitHead / sellCwt : 0, weeks: cell.endorsement_length_weeks };
  })() : null;

  // ⚠ LRP SETTLES ON A NATIONAL INDEX, NOT ON WHAT THE CATTLE BRING HERE.
  // `profitHead` above is the floor on that index and is exactly right as far
  // as it goes — what it is not is a floor on the grower’s cheque.
  //
  // ⚠⚠ AND THE BASIS IS TYPED, NEVER APPLIED FOR THE READER. The first cut
  // of this subtracted the measured NY figure automatically, which put a
  // modelled sample onto a customer-facing number for animals it was never
  // measured on: BASIS_MEASUREMENT is ELEVEN observations of NY FEEDER STEERS
  // in bands 450–850 lb, and this calculator also quotes heifers, Type 821/822
  // and FED cattle. Applying it there is the invented-explanation failure this
  // repo opens with, wearing a measurement for a costume.
  //
  // The sibling "My position" mode of this SAME calculator already had the
  // right shape — a `posBasis` field the user types, defaulting to zero — so
  // auto-applying here also made the two halves of one calculator disagree
  // about basis, which is the class this sweep exists to find.
  //
  // The measurement is OFFERED as a one-click starting point, and only where
  // the animal on screen is inside the population it was measured on. Outside
  // it, the offer is withheld and says why.
  const basisScope = (() => {
    if (species !== "feeder") return { fits: false, why: "measured on feeder steers, and this is fed cattle" };
    if (!/steer/i.test(String(feederType || ""))) return { fits: false, why: `measured on steers, and this is ${feederType || "another type"}` };
    const mids = Object.values(LRP_BAND_MID);
    const lo = Math.min.apply(null, mids), hi = Math.max.apply(null, mids);
    if (!(sellWt >= lo - 50 && sellWt <= hi + 50)) return { fits: false, why: `measured at ${lo}–${hi} lb, and this sells at ${sellWt} lb` };
    return { fits: true, why: null };
  })();
  const planBasisN = num(planBasis);
  const basisFloor = (result && sellCwt > 0 && planBasisN !== 0) ? (() => {
    const adj = planBasisN * sellCwt;
    const profit = result.profitHead + adj;
    return { adjHead: adj, profitHead: profit, profitCwt: profit / sellCwt };
  })() : null;

  // ── STARTING POINTS for a producer who doesn't know their numbers ─────────
  // Both are OFFERED, never applied — the same rule the basis reference and the
  // ration cost-of-gain chip follow. A number on screen changes only because
  // somebody clicked to change it.
  //
  // (a) Buy price from the NY steer auction, through the Push Signal's OWN rule
  //     (thin bands refused, stale weeks labelled). Steer prints only, so it is
  //     offered for steer types and for fed cattle (bought as feeder steers).
  const auctionScope = species === "fed" || /steer/i.test(String(feederType || ""));
  const auction = auctionScope && auctionRows.length && num(buyWt) > 0
    ? lrpResolveInputAnimal(auctionRows, num(buyWt), { asOf: new Date().toISOString().slice(0, 10) })
    : null;
  const auctionPerLb = auction && auction.price_cwt != null ? auction.price_cwt / 100 : null;
  // (b) Ownership costs beyond feed — yardage, vet, trucking, interest on cattle
  //     and feed, commission, checkoff — from the engine's STANDARD budget for the
  //     days actually held. "Other Costs" defaulted to $0, which silently left all
  //     of them out of the floor. Yardage and vet are excluded when the cost of
  //     gain came from the ration calculator, because that figure already carries
  //     them (RationCogCalc) and adding them here would count them twice.
  const usingRationCog = !!(rationCog && Math.abs(rationCog.cog - num(cogPerLb)) <= 0.005);
  const ownOffer = (calcMode === "purchase" && !lotsOn && daysOnFeed != null && daysOnFeed > 0)
    ? lrpOwnershipCosts(species === "fed" ? "fed" : "feeder", "standard", {
        days: daysOnFeed, purchase_hd: purchasePerHead, feed_hd: gainPerHead,
        sale_hd: result ? result.guaranteeHead : null,
        exclude: usingRationCog ? ["yardage", "health"] : [],
      })
    : null;

  // ── SIMPLE MODE'S TYPICAL NUMBERS — every one carries its source ───────────
  const seg = species === "fed" ? "fed" : "feeder";
  const std = LRP_SCENARIOS.standard;
  const feedObj = feedPx ? { corn: Number(feedPx.corn_price), hay: Number(feedPx.alfalfa_hay), soymeal: Number(feedPx.soybean_meal) } : null;
  const autoCog = feedObj ? lrpCogPerLb(seg, "standard", feedObj) : null;
  const feedMonthLbl = feedPx ? new Date(Date.UTC(feedPx.year, feedPx.month - 1, 15)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" }) : null;
  const autoVals = {
    buyPrice: auctionPerLb != null ? auctionPerLb.toFixed(2) : null,
    cogPerLb: autoCog != null ? autoCog.toFixed(2) : null,
    adg: String(std.adg[seg]),
    deathLoss: String(+(std.death_loss_pct[seg] * 100).toFixed(1)),
    otherPerHead: ownOffer ? String(Math.round(ownOffer.total)) : null,
    planBasis: basisScope.fits ? String(LRP_BASIS.mean_cwt) : "0",
  };
  // Simple mode HOLDS the fields at the typical values (they recompute as the
  // weights, dates and markets move). Only values that exist are applied; a
  // missing source leaves the field as it was and the assumptions card says so.
  useEffect(() => {
    if (!simple) return;
    const set = [[autoVals.buyPrice, buyPrice, setBuyPrice], [autoVals.cogPerLb, cogPerLb, setCogPerLb],
      [autoVals.adg, adg, setAdg], [autoVals.deathLoss, deathLoss, setDeathLoss],
      [autoVals.otherPerHead, otherPerHead, setOtherPerHead], [autoVals.planBasis, planBasis, setPlanBasis]];
    for (const [want, have, setter] of set) if (want != null && String(want) !== String(have)) setter(String(want));
    if (lotsOn) setLotsOn(false);
  }, [simple, autoVals.buyPrice, autoVals.cogPerLb, autoVals.adg, autoVals.deathLoss, autoVals.otherPerHead, autoVals.planBasis,
      buyPrice, cogPerLb, adg, deathLoss, otherPerHead, planBasis, lotsOn]);
  const assumptions = [
    ["Buy price", autoVals.buyPrice != null ? `${fmt$(num(autoVals.buyPrice))}/lb` : `${fmt$(num(buyPrice))}/lb (typed default)`,
      auction && auction.price_cwt != null ? `NY steer auction, week of ${fmtDate(auction.week_ending)}${auction.quality !== "strong" ? ` · ${auction.quality} read` : ""}` : (auctionScope ? "no usable NY auction week — the typed default is in use" : "NY auction prints are steers only — the typed default is in use for this class")],
    ["Cost of gain", autoVals.cogPerLb != null ? `${fmt$(num(autoVals.cogPerLb))}/lb` : `${fmt$(num(cogPerLb))}/lb (typed default)`,
      autoCog != null ? `feed only, from ${feedMonthLbl} corn/hay/soymeal (FSA DMC) — Push Signal standard` : "no feed prices loaded — the typed default is in use"],
    ["Rate of gain", `${autoVals.adg} lb/day`, "Push Signal standard"],
    ["Death loss", `${autoVals.deathLoss}%`, "Push Signal standard"],
    ["Costs beyond feed", autoVals.otherPerHead != null ? `${fmt$0(num(autoVals.otherPerHead))}/head` : "—",
      ownOffer ? ownOffer.lines.map(l => `${l.label.toLowerCase()} ${fmt$0(l.value)}`).join(" · ") : "needs a valid end date"],
    ["Local basis", basisScope.fits ? `${fmt$(LRP_BASIS.mean_cwt)}/cwt` : "not applied",
      basisScope.fits ? `mean of ${LRP_BASIS.n} NY feeder-steer sales vs the realised index, ${LRP_BASIS.window}` : `no NY reference: ${basisScope.why}`],
  ];

  // ── DETAILED: costs beyond feed, itemized through the SAME engine rule ────
  const RATE_FIELDS = [
    ["yardage_per_day", "Yardage", "$/hd/day", (v) => v.toFixed(2)],
    ["health_per_hd", "Vet / health", "$/hd", (v) => String(v)],
    ["trucking_per_hd", "Trucking", "$/hd", (v) => String(v)],
    ["interest_apr", "Interest", "% / yr", (v) => (v * 100).toFixed(1)],
    ["commission_pct_of_sale", "Commission", "% of sale", (v) => (v * 100).toFixed(1)],
    ["checkoff_per_hd", "Checkoff", "$/hd", (v) => String(v)],
  ];
  const stdRate = (k) => ({ yardage_per_day: std.yardage_per_day[seg], health_per_hd: std.health_per_hd[seg], trucking_per_hd: std.trucking_per_hd[seg],
    interest_apr: std.interest_apr, commission_pct_of_sale: std.commission_pct_of_sale, checkoff_per_hd: std.checkoff_per_hd }[k]);
  const rateInput = (k) => (ownRates && ownRates[k] != null ? ownRates[k] : RATE_FIELDS.find(f => f[0] === k)[3](stdRate(k)));
  // Percent fields are typed as percents and handed to the engine as fractions.
  const engineRates = Object.fromEntries(RATE_FIELDS.map(([k]) => {
    const raw = rateInput(k);
    const v = parseFloat(raw);
    return [k, raw === "" || !Number.isFinite(v) ? null : (k === "interest_apr" || k === "commission_pct_of_sale" ? v / 100 : v)];
  }));
  const itemized = (!simple && itemize && daysOnFeed != null && daysOnFeed > 0)
    ? lrpOwnershipCosts(seg, "standard", { days: daysOnFeed, purchase_hd: purchasePerHead, feed_hd: gainPerHead,
        sale_hd: result ? result.guaranteeHead : null, rates: engineRates,
        exclude: usingRationCog ? ["yardage", "health"] : [] })
    : null;
  useEffect(() => {
    if (!itemized) return;
    const want = String(Math.round(itemized.total));
    if (want !== String(otherPerHead)) setOtherPerHead(want);
  }, [itemized && Math.round(itemized.total), otherPerHead]);

  // ── THE 60-DAY MARKETING WINDOW — the engine's rule, not a copy ───────────
  // Selling more than 60 days before the end date VOIDS the endorsement (RMA LRP
  // basic provisions). The calculator never checked it: a target weight reached in
  // October could be snapped to a January end date and priced as covered.
  const timing = (calcMode === "purchase" && resolvedEndDate && adgN > 0 && gainLbs > 0)
    ? lrpTimingFit(buyDate, resolvedEndDate, gainLbs, adgN) : null;

  // ── EVERY COVERAGE LEVEL for the chosen end date, side by side ───────────
  const coverageCompare = (resolvedEndDate && inBand && sellCwt > 0)
    ? typeRows.filter(r => r.end_date === resolvedEndDate)
        .map(r => {
          const lvl = Number(r.coverage_level);
          const share = producerShare(r.coverage_level);
          const prem = (Number(r.cost_per_cwt) || 0) * sellCwt * share;
          const cov = Number(r.coverage_price);
          const floor = cov * sellCwt - (costPerSoldHead + prem) + planBasisN * sellCwt;
          return { key: lvl.toFixed(2), lvl, cov, prem, share, floor };
        })
        .sort((a, b) => b.lvl - a.lvl)
    : [];

  // ── WHAT IF THE INDEX SETTLES AT… with and without LRP ───────────────────
  // Without LRP the producer sells at index + basis. With it the indemnity tops
  // the index up to the coverage price, less the premium. Basis is in both (LRP
  // does not cover it); insured weight is taken as the sell weight.
  const whatIf = result ? [0.8, 0.9, 1.0, 1.1, 1.2].map(m => {
    const idx = result.coveragePrice * m;
    const without = (idx + planBasisN) * sellCwt - costPerSoldHead;
    const indem = Math.max(0, result.coveragePrice - idx) * sellCwt;
    const withLrp = without + indem - result.premHead;
    return { m, idx, without, withLrp, indem };
  }) : [];

  // ── THE HEADLINE IS ONE MODEL, read by the screen AND the PDF ─────────────
  // ⚠ A FLOOR ON THE NATIONAL INDEX IS NOT A PROFIT ON THE CHEQUE. With basis at
  // zero and "Other Costs" at $0 the old headline read "✅ Insurable floor profit
  // +$972/head" on its own defaults (2026-10-15), and that figure turned to about
  // −$144/head once the NY auction price, the measured NY basis and the standard
  // ownership costs went in. So the headline only says PROFIT once a basis is
  // entered, and until then it names what the figure leaves out — in the words a
  // producer can act on, with the size of each where we can source it.
  const headlineInfo = result ? (() => {
    const afterBasis = !!basisFloor;
    const value = afterBasis ? basisFloor.profitHead : result.profitHead;
    const missing = [];
    if (!afterBasis) {
      missing.push(basisScope.fits
        ? `your local basis — NY feeder steers have sold about ${fmt$(Math.abs(LRP_BASIS.mean_cwt))}/cwt under the index (${LRP_BASIS.n} sales), about ${fmt$0(Math.abs(LRP_BASIS.mean_cwt) * sellCwt)}/head here`
        : "your local basis — LRP pays on the national index, not on what your cattle bring here");
    }
    if (num(otherPerHead) === 0) {
      missing.push(ownOffer
        ? `costs beyond feed — "Other Costs" is $0; a standard budget for ${daysOnFeed} days is about ${fmt$0(ownOffer.total)}/head`
        : 'costs beyond feed — "Other Costs" is $0');
    }
    return { afterBasis, value, valueCwt: sellCwt > 0 ? value / sellCwt : 0, positive: value >= 0, missing };
  })() : null;

  // ── Per-lot economics ──────────────────────────────────────────────────────
  // Each lot rides the same days on feed and rate of gain, so it puts on the
  // SAME pounds — the lots differ in what they cost to buy and therefore in
  // what they sell at. Cost of gain is identical per head across lots for that
  // reason; only the purchase leg moves.
  //
  // A lot only carries a guarantee when its own sell weight lands inside the
  // endorsement's target band. One outside the band is priced and shown WITHOUT
  // coverage rather than quietly insured at a weight RMA wouldn't accept — the
  // band is per endorsement, not per operation.
  const lotGainLbs = Math.max(0, adgN * (daysOnFeed || 0));
  const lotRows = lots.map((l, i) => {
    const hd    = Math.max(0, Math.round(num(l.head)));
    const inWt  = num(l.buyWt);
    const px    = num(l.buyPrice);
    const outWt = Math.max(inWt, inWt + lotGainLbs);
    const cwt   = outWt / 100;
    const purchase = inWt * px;
    const gain     = lotGainLbs * num(cogPerLb);
    const placed   = purchase + gain + num(otherPerHead);
    const costSold = dl < 1 ? placed / (1 - dl) : placed;
    const lotInBand = band ? (cwt >= band.lo - 0.005 && cwt <= band.hi + 0.005) : false;
    const covered  = !!(cell && lotInBand && cwt > 0);
    const guarantee = covered ? Number(cell.coverage_price) * cwt : 0;
    const prem      = covered ? (Number(cell.cost_per_cwt) || 0) * cwt * producerShare(cell.coverage_level) : 0;
    const totalCost = costSold + prem;
    return { i, hd, inWt, px, outWt, cwt, purchase, gain, placed, costSold, covered, lotInBand,
             guarantee, prem, totalCost,
             profit: covered ? guarantee - totalCost : null,
             beCwt:  cwt > 0 ? totalCost / cwt : 0,
             deathLoss: costSold - placed };
  });
  const lotsLive = lotRows.filter(r => r.hd > 0 && r.inWt > 0);
  // Head-weighted blend. Exact against the per-lot rows: every term is linear in
  // head, so summing dollars and dividing by total head loses nothing.
  const lotsBlend = (() => {
    const hd = lotsLive.reduce((s, r) => s + r.hd, 0);
    if (!hd) return null;
    const sum = (rows, f) => rows.reduce((s, r) => s + f(r) * r.hd, 0);
    const cost = sum(lotsLive, r => r.totalCost), cwtT = sum(lotsLive, r => r.cwt);
    // Floor profit is a COVERED-lots figure. Netting an uncovered lot's cost
    // against a covered lot's guarantee would report a floor the uncovered
    // cattle don't have — the number has to describe the head it applies to.
    const cov = lotsLive.filter(r => r.covered);
    const covHead = cov.reduce((s, r) => s + r.hd, 0);
    const covCost = sum(cov, r => r.totalCost), covGuar = sum(cov, r => r.guarantee);
    return {
      head: hd, totalCost: cost, totalCwt: cwtT,
      costPerHead: cost / hd, beCwt: cwtT > 0 ? cost / cwtT : 0,
      purchase: sum(lotsLive, r => r.purchase), gain: sum(lotsLive, r => r.gain),
      other: sum(lotsLive, () => num(otherPerHead)), deathLoss: sum(lotsLive, r => r.deathLoss),
      prem: sum(lotsLive, r => r.prem),
      coveredHead: covHead, coveredGuarantee: covGuar, coveredCost: covCost,
      profit: covHead > 0 ? covGuar - covCost : null,
      profitPerHead: covHead > 0 ? (covGuar - covCost) / covHead : null,
      allCovered: covHead === hd,
      uncoveredHead: hd - covHead,
    };
  })();

  // ── "My position" P&L (existing owner, coverage already held) ───────────────
  // Premium is sunk (actual $/head). Cost basis uses the actual buy price and the
  // position's sell weight. We sweep the ENDING market price and show net P&L —
  // below the coverage floor the LRP indemnity holds net constant (the floor);
  // above it, net rises with the market and the premium is just a cost.
  const posSellWtN = num(posSellWt);
  const posSellCwt = posSellWtN / 100;
  const posCov     = num(posCovPrice);
  const posPrem    = num(posPremHead);
  const posBasisN  = num(posBasis); // local cash − national index ($/cwt)
  const posGainLbs = Math.max(0, posSellWtN - num(buyWt));
  const posPlacedHead = num(buyWt) * num(buyPrice) + posGainLbs * num(cogPerLb) + num(otherPerHead);
  const posCostHead   = dl < 1 ? posPlacedHead / (1 - dl) : posPlacedHead; // death-loss-adjusted
  // P = the national INDEX at settlement (what LRP settles on). The cattle sell
  // locally at index + basis. LRP pays only on the index, so basis is NOT covered:
  // below the floor, net settles at (coverage + basis), not coverage.
  const posRow = (P) => {
    const cash  = P + posBasisN;                       // local cash sale price
    const sale  = cash * posSellCwt;                   // cattle revenue
    const indem = Math.max(0, posCov - P) * posSellCwt; // LRP pays (coverage − index) when index < coverage
    const net   = sale + indem - posCostHead - posPrem; // ...net of the sunk premium
    return { P, cash, sale, indem, net };
  };
  const posValid = posCov > 0 && posSellCwt > 0 && num(buyPrice) > 0;
  const posLadder = posValid
    ? [0.80, 0.90, 0.95, 1.00, 1.05, 1.10, 1.20, 1.30].map(f => posRow(posCov * f))
    : [];
  const posFloorNet = posValid ? posRow(posCov).net : null;             // guaranteed downside net/head (= (cov+basis)×cwt − cost − prem)
  const posEffFloor = posCov + posBasisN;                                // effective $/cwt floor incl. basis
  const posBreakevenP = posValid ? (posCostHead + posPrem) / posSellCwt - posBasisN : null; // ending INDEX where net=0
  const selEnd = visibleEnds.find(x => String(x.id) === String(selEndId)) || null;
  const posSettled = selEnd && selEnd.actual_end_value != null;
  // The settled LRP payout is admin-only (canSeeLrpIndemnity); this picker is portal-only.
  const posActual = posSettled && canSeeLrpIndemnity() ? (() => {
    const aev = Number(selEnd.actual_end_value);
    const indemTot = Number(selEnd.payable_indemnity || 0);
    const hd = Math.max(1, Number(selEnd.head) || 1);
    const saleHead = (aev + posBasisN) * posSellCwt; // sold locally at settled index + basis
    const netHead  = saleHead + indemTot / hd - posCostHead - posPrem;
    return { aev, indemTot, indemHead: indemTot / hd, netHead };
  })() : null;

  // ⚠ UNBORN FEEDER TYPES ARE LEFT OUT, and the page says so. RMA's 2027 Feeder
  // Cattle endorsement (27-LRP, released April 2026) requires unborn feeder cattle
  // to be insured at a 6.0–9.0 cwt target with an endorsement of AT LEAST 30
  // WEEKS; the rows for types 820/821/822 carry exactly that (6–9 cwt, 30–52 wk).
  // This calculator prices cattle being BOUGHT AND FED, which unborn calves are
  // not. The rate file's names for those codes are unreliable (two codes share
  // "Dairy Heifers Wt 1", two arrive raw as "Type 821/822 Wt 2"), so no name is
  // put on them here — they are identified by RMA's own rule, not by a label.
  const unbornFeederLabels = [...new Set(rates.filter(r => r.commodity === "Feeder Cattle").map(r => r.type_label))]
    .filter(l => { const ws = rates.filter(r => r.type_label === l).map(r => Number(r.endorsement_length_weeks)); return ws.length && Math.min(...ws) >= 30; });
  const feederTypeLabels = [...new Set(rates.filter(r => r.commodity === "Feeder Cattle" && isQuoterFeederLabel(r.type_label)).map(r => r.type_label))]
    .filter(l => !unbornFeederLabels.includes(l)).sort();

  // Is RMA's current-day file likely still pending? Mon–Fri before ~16:30 ET the
  // CME is trading and RMA hasn't published today's rates yet.
  const tradingDayPending = (() => {
    const now = new Date();
    const etHours = (now.getUTCHours() - 4 + 24) % 24; // EDT
    const isWeekday = now.getUTCDay() >= 1 && now.getUTCDay() <= 5;
    const beforePublish = etHours < 16 || (etHours === 16 && now.getUTCMinutes() < 30);
    return isWeekday && beforePublish;
  })();

  const lbl = { fontSize:11, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:4, display:"block" };
  const inp = { width:"100%", padding:"8px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:14, fontVariantNumeric: "tabular-nums", textAlign:"right", boxSizing:"border-box" };
  const ctrl = { width:"100%", padding:"8px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:14, background:"#fff", boxSizing:"border-box" };
  const suffixStyle = { position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", fontSize:10, color:"#677167", pointerEvents:"none" };
  // Right-aligned number + a right-anchored unit suffix. Reserve right padding
  // sized to the suffix so the value never slides under the unit text.
  const field = (label, value, setter, suffix, step="0.01") => (
    <div>
      <span style={lbl}>{label}</span>
      <div style={{ position:"relative" }}>
        <input type="number" step={step} value={value} onChange={e => setter(e.target.value)}
          style={{ ...inp, paddingRight: suffix ? Math.max(26, suffix.length * 7 + 14) : 10 }} />
        {suffix && <span style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", fontSize:10, color:"#677167", pointerEvents:"none" }}>{suffix}</span>}
      </div>
    </div>
  );

  if (loading) return <div style={{ padding:60, textAlign:"center", color:"#60735f" }}>Loading LRP rates…</div>;
  if (!salesEffDate) return (
    <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #d8e0d0" }}>
      <div style={{ fontSize:32, marginBottom:10 }}>🧮</div>
      <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:6 }}>No LRP rate data yet</div>
      <div style={{ fontSize:13, color:"#60735f" }}>The first daily sync will populate rates from RMA's daily file.</div>
    </Card>
  );

  const fresh = freshnessFor(availableDates[0]);

  // Export the current scenario + result to a print-styled HTML doc the user can
  // Save-as-PDF. Same window.open + window.print() pattern as the APH / LRP
  // monthly exports — Clover Ag logo header, no new dependency.
  const exportPDF = () => {
    if (!result) { alert("Set a valid scenario first — the sell weight must be inside the LRP band with a coverage row for the chosen end date."); return; }
    const origin   = (typeof window !== "undefined" && window.location && window.location.origin) || "";
    const LOGO_URL = origin + "/Clover_Ag_Logo1_transparent.png";
    const printDate = new Date().toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric", year:"numeric" });
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;" }[c]));
    const cattle   = species === "fed" ? "Fed Cattle" : feederType;
    const covPct   = Math.round(parseFloat(covLevel) * 100);
    const sharePct = Math.round(producerShare(covLevel) * 100);
    // The PDF reads the SAME headline model as the screen (headlineInfo), so the
    // document a producer keeps cannot call an index floor a profit either.
    const hl = headlineInfo;
    const profitPos = hl.positive;
    const hlNeutral = !hl.afterBasis;
    const kv = (label, val) => `<div class="kv"><span class="k">${esc(label)}</span><span class="v">${val}</span></div>`;

    const scenarioHtml = [
      kv("Cattle", esc(cattle)),
      kv("Coverage level", `${covPct}%`),
      kv("Head", `${headN}`),
      kv("Buy date", esc(fmtDate(buyDate))),
      kv("Buy weight", `${num(buyWt).toLocaleString("en-US")} lb`),
      kv("Buy price", `${fmt$(num(buyPrice))}/lb`),
      kv("Rate of gain", `${adgN.toFixed(2)} lb/day`),
      kv("Cost of gain", `${fmt$(num(cogPerLb))}/lb`),
      kv("Death loss", `${num(deathLoss)}%`),
      kv("Other costs", `${fmt$0(num(otherPerHead))}/head`),
      kv("Basis (cash − index)", hl.afterBasis ? `${fmt$(planBasisN)}/cwt` : "not entered"),
      kv("LRP end date", `${esc(fmtDate(resolvedEndDate))} &middot; ${result.weeks ?? "—"} wk`),
      kv("Days on feed", `${daysOnFeed != null ? daysOnFeed : "—"} days`),
      kv("Sell weight", `${sellWt.toLocaleString("en-US")} lb &middot; ${sellCwt.toFixed(2)} cwt`),
    ].join("");

    const costHtml = [
      kv("Purchase", fmt$(purchasePerHead)),
      kv("Gain (cost of gain)", fmt$(gainPerHead)),
      kv("Other", fmt$(num(otherPerHead))),
      kv("Death loss", fmt$(deathLossPerHead)),
      `<div class="kv total"><span class="k">Total cost / head</span><span class="v">${fmt$(costPerSoldHead)} &middot; ${fmt$(costPerSoldCwt)}/cwt</span></div>`,
    ].join("");

    const tile = (k, v, sub) => `<div class="tile"><div class="t-k">${esc(k)}</div><div class="t-v">${v}</div><div class="t-sub">${esc(sub)}</div></div>`;
    const resultTiles = [
      tile("Coverage floor", `${fmt$(result.coveragePrice)}/cwt`, `${fmt$0(result.guaranteeHead)}/head guarantee`),
      tile("LRP premium", `${fmt$(result.premHead)}/head`, `${sharePct}% producer share`),
      tile("Total cost", `${fmt$0(result.totalCostHead)}/head`, "incl. premium + death loss"),
      tile("Index floor margin", `${fmt$(result.profitHead)}/head`, `${fmt$(result.profitCwt)}/cwt, before basis`),
    ].concat(hl.afterBasis ? [tile("After your basis", `${fmt$(basisFloor.profitHead)}/head`, `${fmt$(planBasisN)}/cwt basis`)] : []).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>LRP Profit Analysis — ${esc(cattle)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; color: #1a2e1a; margin: 0; padding: 32px 36px; background: #fff; }
  .header { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #2d6a2d; padding-bottom: 16px; margin-bottom: 22px; }
  .logo { height: 52px; }
  .title-block { flex: 1; }
  .title { font-size: 21px; font-weight: 800; color: #1a2e1a; }
  .subtitle { font-size: 13px; color: #5a7a5a; margin-top: 2px; }
  .asof { font-size: 11px; color: #677167; margin-top: 4px; }
  .print-btn { background: #2d6a2d; color: #fff; border: none; border-radius: 7px; padding: 9px 16px; font-size: 13px; font-weight: 700; cursor: pointer; }
  .headline { border-radius: 10px; padding: 14px 16px; margin-bottom: 20px; border: 1px solid ${hlNeutral ? "#c9d8e8" : profitPos ? "#bcdcbc" : "#f0cbb0"}; background: ${hlNeutral ? "#eef3f8" : profitPos ? "#eef7ee" : "#fdf0ea"}; }
  .headline .h-k { font-size: 11px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: ${hlNeutral ? "#244a72" : profitPos ? "#1f6f3a" : "#a3561f"}; }
  .headline .h-m { font-size: 12px; color: #2d3f52; margin-top: 6px; line-height: 1.5; }
  .headline .h-v { font-size: 15px; color: #1a2e1a; margin-top: 4px; line-height: 1.5; }
  .headline .big { font-family: monospace; font-weight: 800; color: ${profitPos ? "#1f6f3a" : "#a3271f"}; }
  .sec { margin-bottom: 20px; page-break-inside: avoid; }
  .sec-h { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: #2d4a2d; margin-bottom: 10px; }
  .kvgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 28px; border: 1px solid #d6e3d6; border-radius: 9px; overflow: hidden; }
  .kv { display: flex; justify-content: space-between; gap: 12px; padding: 7px 14px; border-top: 1px solid #f0f4f0; font-size: 13px; }
  .kv:nth-child(1), .kv:nth-child(2) { border-top: none; }
  .kv .k { color: #5a7a5a; }
  .kv .v { font-family: monospace; font-variant-numeric: tabular-nums; font-weight: 700; color: #1a2e1a; }
  .kv.total { background: #f4fbf2; grid-column: 1 / -1; }
  .kv.total .v { color: #1f6f3a; }
  .tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .tile { border: 1px solid #d6e3d6; border-radius: 9px; padding: 12px 14px; }
  .t-k { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #60735f; }
  .t-v { font-size: 18px; font-weight: 800; font-family: monospace; color: #1a2e1a; margin-top: 2px; }
  .t-sub { font-size: 10px; color: #677167; margin-top: 2px; }
  .disclaimer { margin-top: 18px; font-size: 10.5px; color: #60735f; line-height: 1.5; border-top: 1px solid #e0ece0; padding-top: 12px; }
  .footer { margin-top: 18px; padding-top: 14px; border-top: 1px solid #d6e0d6; display: flex; justify-content: space-between; font-size: 10.5px; color: #60735f; }
  .footer .brand { font-weight: 700; color: #2d6a2d; }
  @media print { .no-print { display: none !important; } body { padding: 0; } }
</style></head>
<body>
  <div class="header">
    <img class="logo" src="${LOGO_URL}" alt="Clover Ag Risk Management" onerror="this.style.display='none'"/>
    <div class="title-block">
      <div class="title">LRP Profit Analysis</div>
      <div class="subtitle">${esc(cattle)} &middot; ${covPct}% coverage &middot; ${headN} head</div>
      <div class="asof">RMA rates Sales Effective ${esc(fmtDate(salesEffDate))} &middot; Prepared ${printDate}</div>
    </div>
    <button class="print-btn no-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
  </div>
  <div class="headline">
    <div class="h-k">${hlNeutral ? "Floor on the national index — before local basis" : profitPos ? "✅ Floor after basis" : "⚠️ Floor after basis locks a loss"}</div>
    <div class="h-v">At <strong>${sellWt.toLocaleString("en-US")} lb</strong> on <strong>${esc(fmtDate(resolvedEndDate))}</strong> (${result.weeks ?? "—"} wk &middot; ${daysOnFeed != null ? daysOnFeed : "—"} days on feed), ${covPct}% coverage ${profitPos ? "locks" : "limits the loss to"} <span class="big">${fmt$(hl.value)}/head</span> (${fmt$0(hl.value * headN)} on ${headN} head) if the index settles at or below the coverage price.</div>
    ${hl.missing.length ? `<div class="h-m"><strong>Not in this figure:</strong>${hl.missing.map(m => `<div>&middot; ${esc(m)}</div>`).join("")}</div>` : ""}
  </div>
  <div class="sec">
    <div class="sec-h">Scenario</div>
    <div class="kvgrid">${scenarioHtml}</div>
  </div>
  <div class="sec">
    <div class="sec-h">Cost of Production (per head)</div>
    <div class="kvgrid">${costHtml}</div>
  </div>
  <div class="sec">
    <div class="sec-h">LRP Endorsement Result</div>
    <div class="tiles">${resultTiles}</div>
  </div>
  <div class="disclaimer">
    <strong>Index floor margin = Coverage $/head − Total cost</strong> (total cost includes the LRP premium and death loss) — the floor on the national index if it settles at or below the coverage price. LRP pays on that national index, not on what the cattle bring locally; the difference is basis. Upside above the coverage price is uncapped and not shown. Premium reflects the ${covPct}% coverage subsidy share (${sharePct}% producer cost). Coverage prices and premiums are RMA-published and change daily; the final premium is set when the endorsement is bound. This is an estimate, not a bind — contact Clover Ag to place coverage.
  </div>
  <div class="footer">
    <div class="brand">Clover Ag Risk Management &middot; (585) 233-1066 &middot; cloverag.com</div>
    <div>Generated ${printDate}</div>
  </div>
  <script>window.addEventListener('load', function(){ setTimeout(function(){ try { window.print(); } catch(e){} }, 500); });</script>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) { alert("Popup blocked — allow popups for this site, then click Export again."); return; }
    writePrintWindow(w, html);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems: isMobile ? "stretch" : "center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:10, flexDirection: isMobile ? "column" : "row" }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <div style={{ fontFamily:"'Lora','Georgia',serif", fontSize:isMobile?18:24, fontWeight:700, color:"#1a2e1a" }}>LRP Profit Calculator</div>
            {fresh && (
              <span style={{ fontSize:11, fontWeight:700, color:fresh.color, background:fresh.bg, padding:"3px 8px", borderRadius:4, border:`1px solid ${fresh.color}33` }}>🛰️ {fresh.text}</span>
            )}
          </div>
          <div style={{ fontSize:12, color:"#60735f", marginTop:2 }}>
            {calcMode === "position"
              ? "Cattle you own + LRP you hold → net P&L across ending prices · premium is sunk"
              : <>Cost of production vs one LRP endorsement · pick by end date or target weight · Sales Effective {fmtDate(salesEffDate)}</>}
          </div>
        </div>
        {calcMode === "purchase" && (
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <label style={{ fontSize:11, color:"#60735f", fontWeight:700 }}>Rate date:</label>
          <select value={salesEffDate} onChange={e => changeDate(e.target.value)} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:12, background:"#fff", flex: isMobile ? "1 1 0" : "0 0 auto" }}>
            {availableDates.map(d => <option key={d} value={d}>{fmtDate(d)}</option>)}
          </select>
          {/* The PDF renders the single-lot scenario, so it's off in lots mode
              rather than silently exporting a page that isn't what's on screen. */}
          {!lotsOn && (
          <button onClick={exportPDF} disabled={!result}
            title={result ? "Export this scenario to a printable PDF" : "Set a valid scenario first"}
            style={{ padding:"6px 12px", border:"1px solid #2d6a2d", background: result ? "#2d6a2d" : "#b8c8b8", color:"#fff", borderRadius:6, fontSize:12, fontWeight:700, cursor: result ? "pointer" : "default", whiteSpace:"nowrap" }}>
            📄 Export PDF
          </button>
          )}
        </div>
        )}
      </div>

      {/* Mode toggle: planning a purchase vs analyzing a position already held */}
      <div style={{ display:"flex", gap:6, marginBottom:16, background:"#eef2ee", borderRadius:10, padding:4 }}>
        {[{k:"purchase",l:"📋 Planning a purchase",d:"buy + feed + lock LRP → the floor it insures"},
          {k:"position",l:"📊 My position",d:"cattle owned + LRP held → P&L across the market"}].map(o => (
          <button key={o.k} onClick={() => setCalcMode(o.k)}
            style={{ flex:"1 1 0", padding:"9px 12px", borderRadius:8, cursor:"pointer", textAlign:"left",
              border:"none", background: calcMode===o.k ? "#fff" : "transparent",
              boxShadow: calcMode===o.k ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
            <div style={{ fontWeight:800, fontSize:isMobile?12:13.5, color: calcMode===o.k ? "#1a2e1a" : "#6a806a" }}>{o.l}</div>
            {!isMobile && <div style={{ fontSize:10.5, color:"#8a9a88" }}>{o.d}</div>}
          </button>
        ))}
      </div>

      {/* Trading-day note */}
      {calcMode === "purchase" && tradingDayPending && (
        <div style={{ background:"#fff8e6", border:"1px solid #f0dca0", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12.5, color:"#7a5a00", display:"flex", gap:8, alignItems:"flex-start" }}>
          <span style={{ fontSize:15, lineHeight:1 }}>⏱️</span>
          <span>The market is trading right now, so <strong>today's LRP prices aren't published yet</strong>. RMA releases new rates after the CME closes (~4:30&nbsp;PM&nbsp;ET each business day). The figures below are the most recent published file (Sales Effective <strong>{fmtDate(salesEffDate)}</strong>) — refresh after release to lock in today's coverage.</span>
        </div>
      )}

      {/* Species toggle */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {[{k:"feeder",l:"🐄 Feeder Cattle",d:"backgrounding / selling feeders"},{k:"fed",l:"🐂 Fed Cattle",d:"finishing / selling fats"}].map(o => (
          <button key={o.k} onClick={() => pickSpecies(o.k)}
            style={{ flex:"1 1 200px", padding:"10px 14px", borderRadius:9, cursor:"pointer", textAlign:"left",
              border: species===o.k ? "2px solid #2d6a2d" : "1px solid #d8e0d0",
              background: species===o.k ? "#eef6ee" : "#fff" }}>
            <div style={{ fontWeight:800, fontSize:14, color:"#1a2e1a" }}>{o.l}</div>
            <div style={{ fontSize:11, color:"#60735f" }}>{o.d}</div>
          </button>
        ))}
      </div>

      {/* Feeder type + coverage level (purchase mode; position uses the endorsement's actual coverage) */}
      {calcMode === "purchase" && (
      <div style={{ display:"flex", gap:isMobile?10:16, marginBottom:16, flexWrap:"wrap", alignItems:"flex-end" }}>
        {species === "feeder" && (
          <div style={{ flex:"1 1 220px" }}>
            <span style={lbl}>Feeder Weight Class</span>
            <select value={feederType || ""} onChange={e => setFeederType(e.target.value)}
              style={{ width:"100%", padding:"8px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:14, background:"#fff", boxSizing:"border-box" }}>
              {feederTypeLabels.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <div style={{ marginTop:3, fontSize:10, color:"#a3b3a3" }}>
              Unborn calves (incl. beef-on-dairy day-olds) aren't modelled here — no feeding period. Use the LRP Quoter; RMA limits them to 13–43 weeks from 2027.
            </div>
          </div>
        )}
        <div style={{ flex:"1 1 160px" }}>
          <span style={lbl}>Coverage Level</span>
          <select value={covLevel} onChange={e => setCovLevel(e.target.value)}
            style={{ width:"100%", padding:"8px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:14, background:"#fff", boxSizing:"border-box" }}>
            {(coverageCompare.length ? coverageCompare.map(c => c.key) : ["1.00","0.95","0.90","0.85","0.80"]).map(c => <option key={c} value={c}>{(parseFloat(c)*100).toFixed(parseFloat(c)*100 % 1 ? 1 : 0)}%</option>)}
          </select>
        </div>
      </div>
      )}

      {/* Cost inputs */}
      <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:800, color:"#2d4a2d", textTransform:"uppercase", letterSpacing:"0.06em" }}>Cost of Production</div>
          {calcMode === "purchase" && (
            <div style={{ display:"flex", background:"#eef3ee", borderRadius:20, padding:2 }}>
              {[["simple","Simple — typical numbers"],["detailed","Detailed — my numbers"]].map(([k, lab]) => (
                <button key={k} type="button" onClick={() => setDetailMode(k)}
                  style={{ padding:"5px 12px", borderRadius:18, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:700,
                           background: detailMode === k ? "#2d4a2d" : "transparent", color: detailMode === k ? "#fff" : "#4a6e4a" }}>{lab}</button>
              ))}
            </div>
          )}
          {calcMode === "purchase" && !simple && (
            <label style={{ display:"flex", alignItems:"center", gap:7, fontSize:12.5, color:"#4a6e4a", cursor:"pointer" }}>
              {/* Lots derive their own sell weights, so the single-scenario
                  "pick by target weight" and the manual out-weight override
                  have nothing to act on — snap back to end-date mode. */}
              <input type="checkbox" checked={lotsOn}
                onChange={e => { setLotsOn(e.target.checked); if (e.target.checked) { setPickMode("endDate"); setSellWtOverride(""); } }} />
              Several purchase lots
            </label>
          )}
        </div>

        {simple && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap:12, marginBottom:10 }}>
              {field("Head", head, setHead, "hd", "1")}
              {field("Buy Weight", buyWt, setBuyWt, "lb", "1")}
            </div>
            <div style={{ background:"#f4f8fb", border:"1px solid #d6e2ec", borderRadius:8, padding:"10px 12px" }}>
              <div style={{ fontSize:12, color:"#2d3f52", marginBottom:6 }}>
                <strong>Using typical numbers — not yours.</strong> Everything below is filled in for you and labelled with where it came from. Switch to <button type="button" onClick={() => setDetailMode("detailed")} style={{ background:"none", border:"none", padding:0, cursor:"pointer", color:"#3d6a99", fontWeight:700, textDecoration:"underline", fontFamily:"inherit", fontSize:12 }}>Detailed</button> to use your own.
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <tbody>
                  {assumptions.map(([k, v, src]) => (
                    <tr key={k} style={{ borderTop:"1px solid #e3ebf2" }}>
                      <td style={{ padding:"5px 6px", color:"#5a6f82", whiteSpace:"nowrap" }}>{k}</td>
                      <td style={{ padding:"5px 6px", fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#1a2e1a", whiteSpace:"nowrap" }}>{v}</td>
                      <td style={{ padding:"5px 6px", color:"#7a8c9a", fontSize:11 }}>{src}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize:10.5, color:"#7a8c9a", marginTop:6 }}>Gain, death loss and costs beyond feed are extension-budget typical values, not yet checked against our own closeouts.</div>
            </div>
          </div>
        )}
        {!simple && rationCog && Math.abs(rationCog.cog - num(cogPerLb)) > 0.005 && (
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", background:"#f4fbf2", border:"1px solid #cfe4c8",
            borderRadius:8, padding:"9px 12px", marginBottom:12, fontSize:12.5, color:"#2d4a2d" }}>
            <span>
              Ag Calculators built a cost of gain of <strong style={{ fontVariantNumeric: "tabular-nums" }}>{fmt$(rationCog.cog)}/lb</strong> from your ration
              {rationCog.adg > 0 ? <> at <strong>{rationCog.adg.toFixed(2)} lb/day</strong></> : null}.
            </span>
            <button onClick={() => { setCogPerLb(String(rationCog.cog)); if (rationCog.adg > 0) setAdg(String(rationCog.adg)); }}
              style={{ padding:"4px 11px", borderRadius:6, border:"none", background:"#2d6a2d", color:"#fff", fontSize:11.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              Use it
            </button>
          </div>
        )}

        {simple ? null : lotsOn && calcMode === "purchase" ? (
          <>
            <div style={{ overflowX:"auto", marginBottom:12 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:420 }}>
                <thead><tr>
                  {["Lot", "Head", "Buy weight", "Buy price", "Sell weight", ""].map((h, i) => (
                    <th key={h + i} style={{ textAlign: i === 0 ? "left" : "right", padding:"5px 7px", fontSize:10, fontWeight:800,
                      color:"#60735f", textTransform:"uppercase", letterSpacing:"0.05em", borderBottom:"1px solid #e4ece4" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {lots.map((l, i) => {
                    const row = lotRows[i];
                    return (
                      <tr key={i}>
                        <td style={{ padding:"5px 7px", fontSize:12.5, color:"#60735f", fontWeight:700 }}>{i + 1}</td>
                        <td style={{ padding:"5px 7px", textAlign:"right" }}>
                          <input type="number" min="0" step="1" value={l.head} placeholder="0"
                            onChange={e => setLot(i, { head: e.target.value })} style={{ ...inp, width:74 }} />
                        </td>
                        <td style={{ padding:"5px 7px", textAlign:"right" }}>
                          <input type="number" min="0" step="1" value={l.buyWt} placeholder="0"
                            onChange={e => setLot(i, { buyWt: e.target.value })} style={{ ...inp, width:84 }} />
                        </td>
                        <td style={{ padding:"5px 7px", textAlign:"right" }}>
                          <input type="number" min="0" step="0.01" value={l.buyPrice} placeholder="0.00"
                            onChange={e => setLot(i, { buyPrice: e.target.value })} style={{ ...inp, width:84 }} />
                        </td>
                        <td style={{ padding:"5px 7px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12.5,
                          color: row && row.hd > 0 && !row.lotInBand ? "#a3561f" : "#1a2e1a" }}>
                          {row && row.inWt > 0 ? `${Math.round(row.outWt).toLocaleString("en-US")} lb` : "—"}
                          {row && row.hd > 0 && row.inWt > 0 && !row.lotInBand && (
                            <div style={{ fontSize:10.5, color:"#a3561f" }}>outside band</div>
                          )}
                        </td>
                        <td style={{ padding:"5px 7px", textAlign:"right" }}>
                          <button onClick={() => removeLot(i)} disabled={lots.length <= 1}
                            title={lots.length > 1 ? "Remove this lot" : "At least one lot"}
                            style={{ border:"none", background:"transparent", color: lots.length > 1 ? "#a3561f" : "#d0d8d0",
                              fontSize:16, cursor: lots.length > 1 ? "pointer" : "default", lineHeight:1, padding:"0 4px" }}>×</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button onClick={addLot} style={{ padding:"6px 14px", borderRadius:6, border:"1px dashed #b8ccb0", background:"#f7faf7",
              color:"#2d6a2d", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", marginBottom:14 }}>+ Add lot</button>
            <div style={{ fontSize:11, color:"#677167", marginBottom:12 }}>
              Every lot shares the days on feed, rate of gain, cost of gain and death loss below — they differ in what they cost to buy.
            </div>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap:12 }}>
              {field("Rate of Gain", adg, setAdg, "lb/d", "0.05")}
              {field("Cost of Gain", cogPerLb, setCogPerLb, "$/lb")}
              {field("Death Loss", deathLoss, setDeathLoss, "%", "0.1")}
              {field("Other Costs", otherPerHead, setOtherPerHead, "$/hd", "1")}
            </div>
          </>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap:12 }}>
            {field("Head", head, setHead, "hd", "1")}
            {field("Buy Weight", buyWt, setBuyWt, "lb", "1")}
            {field("Buy Price", buyPrice, setBuyPrice, "$/lb")}
            {field("Rate of Gain", adg, setAdg, "lb/d", "0.05")}
            {field("Cost of Gain", cogPerLb, setCogPerLb, "$/lb")}
            {field("Death Loss", deathLoss, setDeathLoss, "%", "0.1")}
            {field("Other Costs", otherPerHead, setOtherPerHead, "$/hd", "1")}
          </div>
        )}
        {/* Starting points — offered, never applied (see auction / ownOffer above). */}
        {calcMode === "purchase" && !simple && !lotsOn && ((auctionPerLb != null && Math.abs(auctionPerLb - num(buyPrice)) > 0.005) || (ownOffer && Math.abs(ownOffer.total - num(otherPerHead)) > 0.5)) && (
          <div style={{ marginTop:10, background:"#f4f8fb", border:"1px solid #d6e2ec", borderRadius:8, padding:"8px 12px", fontSize:11.5, color:"#2d3f52", lineHeight:1.6 }}>
            <strong>Don't know your numbers?</strong> Starting points — nothing changes unless you click.
            {auctionPerLb != null && Math.abs(auctionPerLb - num(buyPrice)) > 0.005 && (
              <div>
                NY auction, week of {fmtDate(auction.week_ending)}: <strong style={{ fontVariantNumeric: "tabular-nums" }}>{fmt$(auctionPerLb)}/lb</strong> for {num(buyWt)} lb steers
                {auction.quality !== "strong" ? <span style={{ color:"#9a6a2a" }}> ({auction.quality} read{auction.caveats && auction.caveats[0] ? ` — ${auction.caveats[0]}` : ""})</span> : null}
                {" "}<button type="button" onClick={() => setBuyPrice(auctionPerLb.toFixed(2))}
                  style={{ background:"none", border:"none", padding:0, cursor:"pointer", color:"#3d6a99", fontWeight:700, textDecoration:"underline", fontFamily:"inherit", fontSize:11.5 }}>use as buy price</button>
              </div>
            )}
            {ownOffer && Math.abs(ownOffer.total - num(otherPerHead)) > 0.5 && (
              <div>
                Typical costs beyond feed for {daysOnFeed} days: <strong style={{ fontVariantNumeric: "tabular-nums" }}>{fmt$0(ownOffer.total)}/head</strong>
                {" "}({ownOffer.lines.map(l => `${l.label.toLowerCase()} ${fmt$0(l.value)}`).join(" · ")})
                {usingRationCog ? " — yardage and vet left out, your ration cost of gain already includes them" : ""}
                {" "}<button type="button" onClick={() => setOtherPerHead(String(Math.round(ownOffer.total)))}
                  style={{ background:"none", border:"none", padding:0, cursor:"pointer", color:"#3d6a99", fontWeight:700, textDecoration:"underline", fontFamily:"inherit", fontSize:11.5 }}>use as other costs</button>
                <div style={{ fontSize:10.5, color:"#7a8c9a" }}>Extension-budget typical values, not yet checked against our own closeouts. Commission is figured on the index value of the cattle.</div>
              </div>
            )}
          </div>
        )}
        {calcMode === "purchase" && !simple && !lotsOn && (
          <div style={{ marginTop:10 }}>
            <label style={{ display:"flex", alignItems:"center", gap:7, fontSize:12.5, color:"#4a6e4a", cursor:"pointer" }}>
              <input type="checkbox" checked={itemize} onChange={e => setItemize(e.target.checked)} />
              Itemize costs beyond feed (sets "Other Costs")
            </label>
            {itemize && (
              <div style={{ marginTop:8, background:"#f7faf7", border:"1px solid #e0eae0", borderRadius:8, padding:"10px 12px" }}>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(6, 1fr)", gap:10 }}>
                  {RATE_FIELDS.map(([k, lab, unit]) => (
                    <div key={k}>
                      <span style={lbl}>{lab}</span>
                      <input type="number" step="0.01" min="0" value={rateInput(k)}
                        onChange={ev => setOwnRates(r => ({ ...(r || {}), [k]: ev.target.value }))} style={ctrl} />
                      <div style={{ fontSize:10, color:"#677167" }}>{unit}</div>
                    </div>
                  ))}
                </div>
                {itemized && (
                  <div style={{ fontSize:11.5, color:"#2d3f52", marginTop:8, lineHeight:1.6 }}>
                    {itemized.lines.map(l => `${l.label} ${fmt$0(l.value)} (${l.detail})`).join(" · ")}
                    {" "}= <strong style={{ fontVariantNumeric: "tabular-nums" }}>{fmt$0(itemized.total)}/head</strong>
                    {usingRationCog ? " — yardage and vet left out, your ration cost of gain already includes them" : ""}
                    {itemized.ignored_overrides.length ? <span style={{ color:"#a3561f" }}> — ignored (not a number ≥ 0): {itemized.ignored_overrides.join(", ")}</span> : null}
                    {" "}<button type="button" onClick={() => setOwnRates(null)} style={{ background:"none", border:"none", padding:0, cursor:"pointer", color:"#3d6a99", textDecoration:"underline", fontFamily:"inherit", fontSize:11.5 }}>reset to standard</button>
                  </div>
                )}
                {!itemized && <div style={{ fontSize:11.5, color:"#677167", marginTop:8 }}>Pick an end date to price the time-based lines.</div>}
              </div>
            )}
          </div>
        )}
        {/* ⚠ PURCHASE MODE ONLY. "My position" already has its OWN basis field
            (posBasis) for cattle already owned, and rendering this one there put TWO
            "Basis (cash - index)" inputs on one screen, only one of which did
            anything. Caught by driving the neighbouring mode after the change. */}
        {calcMode === "purchase" && !simple && (
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap:12, marginTop:12 }}>
          {/* ⚠ LRP PAYS ON A NATIONAL INDEX; THE GROWER SELLS LOCALLY. The gap is
              basis and LRP does not cover it, so the floor above is a floor on the
              index and not on the cheque. This is TYPED and defaults to zero —
              the same shape the "My position" mode already uses, so the two halves
              of this calculator cannot disagree. The measured NY figure is offered
              as one click, and only for the animal it was measured on. */}
          <div>
            <span style={lbl}>Basis (cash — index)</span>
            <input type="number" step="0.01" value={planBasis} onChange={e => setPlanBasis(e.target.value)} style={ctrl} />
            {basisScope.fits
              ? <button type="button" onClick={() => setPlanBasis(String(LRP_BASIS.mean_cwt))}
                  title={`Mean of ${LRP_BASIS.n} matched NY feeder-steer sales against the realised index, ${LRP_BASIS.window}. A reference point, not your basis.`}
                  style={{ marginTop:3, background:"none", border:"none", padding:0, cursor:"pointer", fontSize:10,
                           color:"#5a7a5a", textDecoration:"underline dotted", fontFamily:"inherit" }}>
                  use NY steer reference ({fmt$(LRP_BASIS.mean_cwt)}, n={LRP_BASIS.n})
                </button>
              : <div style={{ marginTop:3, fontSize:10, color:"#a3b3a3" }}>no NY reference: {basisScope.why}</div>}
          </div>
        </div>
        )}
      </div>

      {/* LRP endorsement / scenario picker (purchase mode) */}
      {calcMode === "purchase" && (
      <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
          <div style={{ fontSize:12, fontWeight:800, color:"#2d4a2d", textTransform:"uppercase", letterSpacing:"0.06em" }}>LRP Endorsement</div>
          <div style={{ display:"flex", gap:4, background:"#f0f4f0", borderRadius:8, padding:3, visibility: lotsOn ? "hidden" : "visible" }}>
            {[{k:"endDate",l:"By end date"},{k:"targetWt",l:"By target weight"}].map(o => (
              <button key={o.k} onClick={() => { setPickMode(o.k); setSellWtOverride(""); }}
                style={{ padding:"5px 12px", borderRadius:6, border:"none", cursor:"pointer", fontSize:12, fontWeight:700,
                  background: pickMode===o.k ? "#fff" : "transparent", color: pickMode===o.k ? "#1a2e1a" : "#60735f",
                  boxShadow: pickMode===o.k ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}>{o.l}</button>
            ))}
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap:12, alignItems:"end" }}>
          <div>
            <span style={lbl}>Buy / Placement Date</span>
            <input type="date" value={buyDate} onChange={e => setBuyDate(e.target.value)} style={ctrl} />
          </div>
          {pickMode === "endDate" ? (
            <div>
              <span style={lbl}>LRP End Date</span>
              <select value={endDateSel || resolvedEndDate || ""} onChange={e => setEndDateSel(e.target.value)} style={ctrl}>
                {availableEndDates.length === 0 && <option value="">—</option>}
                {availableEndDates.map(d => <option key={d} value={d}>{fmtDate(d)} · {weeksByEnd[d]}wk</option>)}
              </select>
            </div>
          ) : (
            <div>
              <span style={lbl}>Target Sell Weight</span>
              <div style={{ position:"relative" }}>
                <input type="number" step="1" value={targetWt} onChange={e => { setTargetWt(e.target.value); setSellWtOverride(""); }} style={{ ...inp, paddingRight:26 }} />
                <span style={suffixStyle}>lb</span>
              </div>
            </div>
          )}
          {!lotsOn && (
          <div>
            <span style={lbl}>Sell Weight {sellWtOverride === "" ? <span style={{ color:"#677167", fontWeight:600 }}>· auto</span> : <button onClick={() => setSellWtOverride("")} style={{ background:"none", border:"none", color:"#2d6a2d", fontSize:10, fontWeight:700, cursor:"pointer", padding:0 }}>↺ auto</button>}</span>
            <div style={{ position:"relative" }}>
              <input type="number" step="1" value={sellWtOverride !== "" ? sellWtOverride : Math.round(derivedSellWt)} onChange={e => setSellWtOverride(e.target.value)} style={{ ...inp, paddingRight:26 }} />
              <span style={suffixStyle}>lb</span>
            </div>
          </div>
          )}
          <div>
            <span style={lbl}>Days on Feed</span>
            <div style={{ padding:"8px 10px", borderRadius:6, border:"1px solid #eef2ee", background:"#f7faf7", fontSize:14, fontVariantNumeric: "tabular-nums", fontWeight:700, color:"#1a2e1a", textAlign:"right" }}>
              {daysOnFeed != null && daysOnFeed >= 0 ? `${daysOnFeed} d` : "—"}
            </div>
          </div>
        </div>
        <div style={{ fontSize:11.5, color:"#60735f", marginTop:10, lineHeight:1.5 }}>
          {lotsOn
            ? <>Each lot sells at its own buy weight + <strong>{adgN.toFixed(2)} lb/day</strong> × {daysOnFeed != null ? daysOnFeed : "—"} days on feed, and insures that weight off the same coverage price.</>
            : pickMode === "targetWt"
            ? (readyDate
                ? <>At <strong>{adgN.toFixed(2)} lb/day</strong> they'd reach <strong>{num(targetWt)} lb</strong> around <strong>{fmtDate(readyDate)}</strong> ({targetDays != null ? Math.round(targetDays) : "—"} days on feed). Nearest LRP end date: <strong>{fmtDate(resolvedEndDate)}</strong>.</>
                : <>Enter a target weight above the buy weight and a positive rate of gain.</>)
            : <>Sell weight = buy weight + <strong>{adgN.toFixed(2)} lb/day</strong> × {daysOnFeed != null ? daysOnFeed : "—"} days on feed. Override it if your actual out-weight differs.</>}
          {band && <> · LRP band for <strong>{species === "fed" ? "Fed Cattle" : feederType}</strong>: {band.lo}–{band.hi} cwt{!lotsOn && !inBand && <span style={{ color:"#9a1f1f" }}> · ⚠️ {sellCwt.toFixed(2)} cwt is outside this band — adjust the weight or class</span>}.</>}
        </div>
      </div>
      )}

      {/* Per-lot breakeven + floor profit (multi-lot purchase mode) */}
      {calcMode === "purchase" && lotsOn && (
        !lotsBlend ? (
          <Card style={{ textAlign:"center", padding:"30px 20px", border:"1px dashed #d8e0d0", color:"#60735f" }}>
            Enter head and a buy weight on at least one lot.
          </Card>
        ) : (
          <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 16px", marginBottom:18 }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#2d4a2d", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12 }}>
              Breakeven by lot
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:640 }}>
                <thead><tr>
                  {["Lot", "Head", "Buy wt", "Buy $/lb", "Sell wt", "Cost/head", "Breakeven", "Guarantee", "Floor profit"].map((h, i) => (
                    <th key={h} style={{ textAlign: i === 0 ? "left" : "right", padding:"6px 8px", fontSize:10, fontWeight:800,
                      color:"#60735f", textTransform:"uppercase", letterSpacing:"0.05em", borderBottom:"1px solid #e4ece4" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {lotsLive.map(r => (
                    <tr key={r.i}>
                      <td style={{ padding:"7px 8px", fontSize:12.5, color:"#60735f", fontWeight:700 }}>{r.i + 1}</td>
                      {[r.hd.toLocaleString("en-US"), `${Math.round(r.inWt).toLocaleString("en-US")} lb`, fmt$(r.px),
                        `${Math.round(r.outWt).toLocaleString("en-US")} lb`, fmt$(r.totalCost), `${fmt$(r.beCwt)}/cwt`].map((v, j) => (
                        <td key={j} style={{ padding:"7px 8px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12.5, color:"#1a2e1a" }}>{v}</td>
                      ))}
                      <td style={{ padding:"7px 8px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12.5, color: r.covered ? "#1a2e1a" : "#a3561f" }}>
                        {r.covered ? fmt$(r.guarantee) : "no coverage"}
                      </td>
                      <td style={{ padding:"7px 8px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12.5, fontWeight:800,
                        color: r.profit == null ? "#677167" : r.profit >= 0 ? "#1f6f3a" : "#a3271f" }}>
                        {r.profit == null ? "—" : `${r.profit >= 0 ? "+" : ""}${fmt$(r.profit)}`}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background:"#f4fbf2", borderTop:"2px solid #d8e6d8" }}>
                    <td colSpan={2} style={{ padding:"8px", fontSize:12, fontWeight:800, color:"#1a2e1a" }}>
                      Blended · {lotsBlend.head.toLocaleString("en-US")} hd
                    </td>
                    <td colSpan={3} />
                    <td style={{ padding:"8px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12.5, fontWeight:800, color:"#1a2e1a" }}>{fmt$(lotsBlend.costPerHead)}</td>
                    <td style={{ padding:"8px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12.5, fontWeight:800, color:"#1a2e1a" }}>{fmt$(lotsBlend.beCwt)}/cwt</td>
                    <td style={{ padding:"8px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12.5, color:"#60735f" }}>
                      {lotsBlend.coveredHead > 0 ? fmt$0(lotsBlend.coveredGuarantee) : "—"}
                    </td>
                    <td style={{ padding:"8px", textAlign:"right", fontVariantNumeric: "tabular-nums", fontSize:12.5, fontWeight:800,
                      color: lotsBlend.profit == null ? "#677167" : lotsBlend.profit >= 0 ? "#1f6f3a" : "#a3271f" }}>
                      {lotsBlend.profit == null ? "—" : `${lotsBlend.profit >= 0 ? "+" : ""}${fmt$0(lotsBlend.profit)}`}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ fontSize:11, color:"#677167", marginTop:10, lineHeight:1.6 }}>
              Breakeven is cost per hundredweight sold, LRP premium and death loss included.
              {lotsBlend.coveredHead > 0
                ? <> Floor profit totals <strong>{lotsBlend.coveredHead.toLocaleString("en-US")}</strong> covered head — what they net if the market settles at or below the coverage price.</>
                : <> No lot lands inside the {species === "fed" ? "Fed Cattle" : feederType} target band{band && <> ({band.lo}–{band.hi} cwt)</>}, so nothing here is insured.</>}
              {lotsBlend.uncoveredHead > 0 && lotsBlend.coveredHead > 0 && (
                <> <span style={{ color:"#a3561f" }}>{lotsBlend.uncoveredHead.toLocaleString("en-US")} head sit outside the band and carry no floor — they're in the breakeven but not the profit.</span></>
              )}
            </div>
          </div>
        )
      )}

      {/* Cost breakdown (purchase mode, single lot) */}
      {calcMode === "purchase" && !lotsOn && (
      <div style={{ background:"#f7faf7", border:"1px solid #e0eae0", borderRadius:10, padding:"12px 16px", marginBottom:18 }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:isMobile?10:0, justifyContent:"space-between" }}>
          {[
            ["Purchase", purchasePerHead, `${num(buyWt)} lb × ${fmt$(num(buyPrice))}`],
            ["Gain", gainPerHead, `${gainLbs.toFixed(0)} lb × ${fmt$(num(cogPerLb))}`],
            ["Other", num(otherPerHead), "misc / head"],
            ["Death loss", deathLossPerHead, `${num(deathLoss)}% spread`],
          ].map(([k,v,sub]) => (
            <div key={k} style={{ flex:"1 1 120px", minWidth:110 }}>
              <div style={{ fontSize:10, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.05em" }}>{k}</div>
              <div style={{ fontSize:16, fontWeight:800, color:"#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{fmt$(v)}</div>
              <div style={{ fontSize:10, color:"#677167" }}>{sub}</div>
            </div>
          ))}
          <div style={{ flex:"1 1 140px", minWidth:130, borderLeft: isMobile ? "none" : "2px solid #d8e6d8", paddingLeft: isMobile ? 0 : 14 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#2d6a2d", textTransform:"uppercase", letterSpacing:"0.05em" }}>Total cost / head</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#1a2e1a", fontVariantNumeric: "tabular-nums" }}>{fmt$(costPerSoldHead)}</div>
            <div style={{ fontSize:10, color:"#677167" }}>{fmt$(costPerSoldCwt)}/cwt · before LRP premium</div>
          </div>
        </div>
      </div>

      )}

      {/* Single-endorsement result (purchase mode, single lot) */}
      {calcMode === "purchase" && !lotsOn && (!resolvedEndDate ? (
        <Card style={{ textAlign:"center", padding:"30px 20px", border:"1px dashed #d8e0d0", color:"#60735f" }}>
          No LRP end dates available for {species === "fed" ? "Fed Cattle" : feederType} on {fmtDate(salesEffDate)}. Try another rate date or class.
        </Card>
      ) : !inBand ? (
        <Card style={{ textAlign:"center", padding:"30px 20px", border:"1px dashed #e0c0a0", color:"#9a6a2a" }}>
          Sell weight <strong>{sellCwt.toFixed(2)} cwt</strong> is outside the LRP target band{band && <> ({band.lo}–{band.hi} cwt)</>} for {species === "fed" ? "Fed Cattle" : feederType}. Adjust the weight, ADG, or {species === "feeder" ? "feeder weight class" : "rate date"} above.
        </Card>
      ) : !result ? (
        <Card style={{ textAlign:"center", padding:"30px 20px", border:"1px dashed #d8e0d0", color:"#60735f" }}>
          No {Math.round(parseFloat(covLevel)*100)}% coverage row for {fmtDate(resolvedEndDate)} on {fmtDate(salesEffDate)}. Try another coverage level or end date.
        </Card>
      ) : (
        <>
          {timing && !timing.eligible && (
            <div style={{ background:"#fdf0ea", border:"1px solid #f0cbb0", borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:13, color:"#7a3a12" }}>
              <strong>⚠ Timing:</strong> at {adgN.toFixed(2)} lb/day these cattle are market-ready {fmtDate(timing.ready_date)} — {timing.reason}. RMA requires owning them until the last 60 days before the end date ({fmtDate(timing.window_opens)} – {fmtDate(resolvedEndDate)}).
            </div>
          )}
          <div style={{ background: !headlineInfo.afterBasis ? "#eef3f8" : headlineInfo.positive ? "#eef7ee" : "#fdf0ea", border:`1px solid ${!headlineInfo.afterBasis ? "#c9d8e8" : headlineInfo.positive ? "#bcdcbc" : "#f0cbb0"}`, borderRadius:10, padding:"14px 16px", marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:800, color: !headlineInfo.afterBasis ? "#244a72" : headlineInfo.positive ? "#1f6f3a" : "#a3561f", textTransform:"uppercase", letterSpacing:"0.05em" }}>
              {!headlineInfo.afterBasis ? "Floor on the national index — before your local basis"
                : headlineInfo.positive ? "✅ Floor after your basis" : "⚠️ Floor after your basis locks a loss"}
            </div>
            <div style={{ fontSize:15, color:"#1a2e1a", marginTop:4, lineHeight:1.5 }}>
              At <strong>{sellWt} lb</strong> on <strong>{fmtDate(resolvedEndDate)}</strong> ({result.weeks ?? "—"} wk · {daysOnFeed != null ? `${daysOnFeed} days on feed` : "—"}), {Math.round(parseFloat(covLevel)*100)}% coverage {headlineInfo.positive ? "locks" : "limits the loss to"}{" "}
              <strong style={{ fontVariantNumeric: "tabular-nums", color: !headlineInfo.afterBasis ? "#244a72" : headlineInfo.positive ? "#1f6f3a" : "#a3271f" }}>{fmt$(headlineInfo.value)}/head</strong>
              {" "}({fmt$0(headlineInfo.value * headN)} on {headN} head) if the index settles at or below your coverage price.
            </div>
            {headlineInfo.missing.length > 0 && (
              <div style={{ fontSize:12.5, color:"#2d3f52", marginTop:6, lineHeight:1.5 }}>
                <strong>Not in this figure:</strong>
                {headlineInfo.missing.map((m, i) => <div key={i}>· {m}</div>)}
              </div>
            )}
          </div>
          <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 16px" }}>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap:14 }}>
              {[
                ["Coverage floor", `${fmt$(result.coveragePrice)}/cwt`, `${fmt$0(result.guaranteeHead)}/head guarantee`, "#1a4a7a"],
                ["LRP premium", `${fmt$(result.premHead)}/head`, `${Math.round(producerShare(covLevel)*100)}% producer share`, "#7a4a00"],
                ["Total cost", `${fmt$0(result.totalCostHead)}/head`, "incl. premium + death loss", "#5a5a5a"],
                ["Index floor margin", `${fmt$(result.profitHead)}/head`, `${fmt$(result.profitCwt)}/cwt, before basis`, result.profitHead >= 0 ? "#1f6f3a" : "#a3271f"],
              ].concat(basisFloor ? [[
                "After your basis", `${fmt$(basisFloor.profitHead)}/head`,
                `${fmt$(planBasisN)}/cwt ${simple ? "NY steer reference basis" : "basis you entered"}`,
                basisFloor.profitHead >= 0 ? "#1f6f3a" : "#a3271f",
              ]] : []).map(([k,v,sub,color]) => (
                <div key={k}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.05em" }}>{k}</div>
                  <div style={{ fontSize:18, fontWeight:800, color, fontVariantNumeric: "tabular-nums" }}>{v}</div>
                  <div style={{ fontSize:10, color:"#677167" }}>{sub}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:12, paddingTop:10, borderTop:"1px solid #f0f4f0", fontSize:10.5, color:"#677167", lineHeight:1.5 }}>
              <strong>Index floor margin = Coverage $/head − Total cost</strong> (total cost includes the LRP premium and death loss) — the floor <em>on the national index</em> if it settles at or below your coverage price. Upside above the coverage price is uncapped and not shown.
              {" "}<strong>LRP settles on that national index, not on what your cattle bring here.</strong>{" "}{simple ? (basisScope.fits ? "Simple mode applied the NY steer reference as your basis — switch to Detailed to enter your own." : "Simple mode applies no basis for this animal.") : "Local basis is yours to enter above and defaults to zero; nothing is applied for you."}{basisFloor ? ` “After your basis” uses the ${fmt$(planBasisN)}/cwt ${simple ? "NY steer reference" : "you typed"}.` : ""}{basisScope.fits ? <>{" "}For reference, {LRP_BASIS.n} matched NY feeder-steer sales over {LRP_BASIS.window} ran {fmt$(LRP_BASIS.mean_cwt)}/cwt against the realised index (median {fmt$(LRP_BASIS.median_cwt)}, range {fmt$(LRP_BASIS.max_cwt)} to {fmt$(LRP_BASIS.min_cwt)}), and not one was materially positive — eleven observations, so a reference point rather than your basis.</> : <>{" "}Our NY basis measurement does not cover this animal {LRP_BASIS.n && ""}({basisScope.why}), so no reference figure is offered.</>} Premium reflects the {Math.round(parseFloat(covLevel)*100)}% coverage subsidy share ({Math.round(producerShare(covLevel)*100)}% producer cost). Coverage & premium are RMA-published; final premium is set when the endorsement is bound. Not a bind — contact Clover Ag to place coverage.
            </div>
          </div>

          {coverageCompare.length > 1 && (
            <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 16px", marginTop:14 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#2d4a2d", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4 }}>Every coverage level · {fmtDate(resolvedEndDate)}</div>
              <div style={{ fontSize:11.5, color:"#60735f", marginBottom:8 }}>Same cattle, same costs{basisFloor ? ", your basis included" : ", before local basis"}. Higher coverage raises the floor and the premium — the floor column nets both. Click a row to use it.</div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
                  <thead><tr style={{ color:"#60735f", textAlign:"right", fontSize:11 }}>
                    <th style={{ textAlign:"left", padding:"4px 8px" }}>Coverage</th><th style={{ padding:"4px 8px" }}>Coverage price</th>
                    <th style={{ padding:"4px 8px" }}>Your premium</th><th style={{ padding:"4px 8px" }}>{basisFloor ? "Floor after basis" : "Index floor margin"}</th>
                  </tr></thead>
                  <tbody>
                    {coverageCompare.map(c => (
                      <tr key={c.key} onClick={() => setCovLevel(c.key)}
                        style={{ cursor:"pointer", borderTop:"1px solid #f0f4f0", textAlign:"right", background: c.key === covLevel ? "#eef7ee" : "transparent", fontWeight: c.key === covLevel ? 800 : 400 }}>
                        <td style={{ textAlign:"left", padding:"5px 8px" }}>{(c.lvl * 100).toFixed(c.lvl * 100 % 1 ? 1 : 0)}%</td>
                        <td style={{ padding:"5px 8px", fontVariantNumeric: "tabular-nums" }}>{fmt$(c.cov)}/cwt</td>
                        <td style={{ padding:"5px 8px", fontVariantNumeric: "tabular-nums" }}>{fmt$(c.prem)}/hd <span style={{ color:"#677167", fontSize:10.5 }}>({Math.round(c.share * 100)}% share)</span></td>
                        <td style={{ padding:"5px 8px", fontVariantNumeric: "tabular-nums", color: c.floor >= 0 ? "#1f6f3a" : "#a3271f" }}>{fmt$(c.floor)}/hd</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {whatIf.length > 0 && (
            <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 16px", marginTop:14 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#2d4a2d", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4 }}>What if the index settles at…</div>
              <div style={{ fontSize:11.5, color:"#60735f", marginBottom:8 }}>Net per head at sale, {basisFloor ? `with your ${fmt$(planBasisN)}/cwt basis` : "before local basis"}. LRP pays the gap below the coverage price; above it you keep the market and the premium is the cost.</div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
                  <thead><tr style={{ color:"#60735f", textAlign:"right", fontSize:11 }}>
                    <th style={{ textAlign:"left", padding:"4px 8px" }}>Index at end date</th><th style={{ padding:"4px 8px" }}>LRP pays</th>
                    <th style={{ padding:"4px 8px" }}>Without LRP</th><th style={{ padding:"4px 8px" }}>With LRP</th>
                  </tr></thead>
                  <tbody>
                    {whatIf.map(w => (
                      <tr key={w.m} style={{ borderTop:"1px solid #f0f4f0", textAlign:"right", background: w.m === 1 ? "#f7faf7" : "transparent" }}>
                        <td style={{ textAlign:"left", padding:"5px 8px" }}><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt$(w.idx)}/cwt</span> <span style={{ color:"#677167", fontSize:10.5 }}>{w.m === 1 ? "= coverage price" : `${w.m > 1 ? "+" : "−"}${Math.round(Math.abs(w.m - 1) * 100)}%`}</span></td>
                        <td style={{ padding:"5px 8px", fontVariantNumeric: "tabular-nums" }}>{fmt$0(w.indem)}/hd</td>
                        <td style={{ padding:"5px 8px", fontVariantNumeric: "tabular-nums", color: w.without >= 0 ? "#1f6f3a" : "#a3271f" }}>{fmt$0(w.without)}/hd</td>
                        <td style={{ padding:"5px 8px", fontVariantNumeric: "tabular-nums", fontWeight:800, color: w.withLrp >= 0 ? "#1f6f3a" : "#a3271f" }}>{fmt$0(w.withLrp)}/hd</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ))}

      {/* ─── MY POSITION (existing owner, coverage already held) ───────────── */}
      {calcMode === "position" && (
        <>
          {/* Coverage held (prefill from a real endorsement or enter manually) */}
          <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#2d4a2d", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>Coverage You Hold</div>
            {/* Portal-only. visibleEnds is filtered to the current customer's farms
                (see visibleEnds) — a farmer/preview sees only that customer's own. */}
            {isPortal && visibleEnds.length > 0 && (
              <div style={{ marginBottom:12 }}>
                <span style={lbl}>Load from your LRP coverage</span>
                <select value={selEndId} onChange={e => setSelEndId(e.target.value)} style={ctrl}>
                  <option value="">— manual entry —</option>
                  {visibleEnds.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.farmer} · {e.commodity} · ends {fmtDate(e.end_date)} · {e.head} hd · ${Number(e.coverage_price).toFixed(2)}/cwt{e.actual_end_value != null ? " · settled" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap:12 }}>
              {field("Coverage Price (floor)", posCovPrice, setPosCovPrice, "$/cwt")}
              {field("Premium Paid", posPremHead, setPosPremHead, "$/hd")}
              {field("Sell Weight", posSellWt, setPosSellWt, "lb", "1")}
              {field("Basis (cash − index)", posBasis, setPosBasis, "$/cwt")}
            </div>
            <div style={{ fontSize:11, color:"#60735f", marginTop:8, lineHeight:1.5 }}>
              Premium is <strong>already paid</strong> (a sunk cost — not re-quoted). <strong>Basis</strong> = your local cash price minus the national index LRP settles on (negative if you sell under the index) — LRP does <em>not</em> cover basis. Cost basis comes from the <strong>Cost of Production</strong> inputs above.
            </div>
          </div>

          {posValid ? (
            <>
              {/* Summary tiles */}
              <div style={{ background: posFloorNet >= 0 ? "#eef7ee" : "#fdf0ea", border:`1px solid ${posFloorNet >= 0 ? "#bcdcbc" : "#f0cbb0"}`, borderRadius:10, padding:"14px 16px", marginBottom:14 }}>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap:14 }}>
                  {[
                    ["Cost basis", `${fmt$0(posCostHead)}/head`, "cattle + gain + death loss", "#5a5a5a"],
                    ["Premium (sunk)", `${fmt$(posPrem)}/head`, "already paid", "#7a4a00"],
                    ["LRP floor", `${fmt$(posCov)}/cwt`, posBasisN !== 0 ? `${fmt$(posEffFloor)}/cwt after basis` : `${fmt$0(posCov*posSellCwt)}/head guarantee`, "#1a4a7a"],
                    ["Floor net", `${fmt$(posFloorNet)}/head`, posBasisN !== 0 ? "if index ≤ floor · incl. basis" : "if market ≤ floor · guaranteed", posFloorNet >= 0 ? "#1f6f3a" : "#a3271f"],
                  ].map(([k,v,sub,color]) => (
                    <div key={k}>
                      <div style={{ fontSize:10, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.05em" }}>{k}</div>
                      <div style={{ fontSize:17, fontWeight:800, color, fontVariantNumeric: "tabular-nums" }}>{v}</div>
                      <div style={{ fontSize:10, color:"#677167" }}>{sub}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop:12, paddingTop:10, borderTop:"1px solid rgba(0,0,0,0.06)", fontSize:12.5, color:"#1a2e1a", lineHeight:1.5 }}>
                  Breakeven ending index: <strong style={{ fontVariantNumeric: "tabular-nums" }}>{fmt$(posBreakevenP)}/cwt</strong>{posBreakevenP <= posCov
                    ? <> — <em>below</em> your <strong>{fmt$(posCov)}</strong> coverage, so the LRP <strong>guarantees a profit</strong> at this cost basis{posBasisN !== 0 ? " and basis" : ""}.</>
                    : <> — <em>above</em> your <strong>{fmt$(posCov)}</strong> coverage, so the index must finish here to break even; below coverage your net is capped at the guaranteed floor above{posBasisN < 0 ? " (basis erodes it — LRP won't cover that gap)" : ""}.</>}
                </div>
              </div>

              {/* Scenario table */}
              <div style={{ background:"#fff", border:"1px solid #e0eae0", borderRadius:10, padding:"14px 16px", marginBottom:14, overflowX:"auto" }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#2d4a2d", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>Net P&amp;L by Ending Market Price</div>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:480 }}>
                  <thead>
                    <tr style={{ color:"#60735f", fontSize:11, textTransform:"uppercase" }}>
                      <th style={{ textAlign:"left", padding:"6px 8px" }}>Ending index $/cwt</th>
                      {posBasisN !== 0 && <th style={{ textAlign:"right", padding:"6px 8px" }}>Your cash $/cwt</th>}
                      <th style={{ textAlign:"right", padding:"6px 8px" }}>Cattle sale/hd</th>
                      <th style={{ textAlign:"right", padding:"6px 8px" }}>LRP pays/hd</th>
                      <th style={{ textAlign:"right", padding:"6px 8px" }}>Net/head</th>
                      <th style={{ textAlign:"right", padding:"6px 8px" }}>Net total</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                    {posLadder.map((r, i) => {
                      const atFloor = r.P <= posCov + 0.001;
                      return (
                        <tr key={i} style={{ borderTop:"1px solid #f0f4f0", background: atFloor ? "#f3f8f3" : "transparent" }}>
                          <td style={{ textAlign:"left", padding:"6px 8px", fontWeight:700 }}>{fmt$(r.P)}{Math.abs(r.P - posCov) < 0.001 && <span style={{ color:"#1a7a4a", fontSize:10 }}> ← floor</span>}</td>
                          {posBasisN !== 0 && <td style={{ textAlign:"right", padding:"6px 8px", color:"#60735f" }}>{fmt$(r.cash)}</td>}
                          <td style={{ textAlign:"right", padding:"6px 8px" }}>{fmt$0(r.sale)}</td>
                          <td style={{ textAlign:"right", padding:"6px 8px", color: r.indem > 0 ? "#1a7a4a" : "#b0b8b0" }}>{r.indem > 0 ? fmt$0(r.indem) : "—"}</td>
                          <td style={{ textAlign:"right", padding:"6px 8px", fontWeight:800, color: r.net >= 0 ? "#1f6f3a" : "#a3271f" }}>{fmt$(r.net)}</td>
                          <td style={{ textAlign:"right", padding:"6px 8px", fontWeight:700, color: r.net >= 0 ? "#1f6f3a" : "#a3271f" }}>{fmt$0(r.net * Math.max(1, Math.round(num(head))))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ fontSize:10.5, color:"#677167", marginTop:10, lineHeight:1.5 }}>
                  The ladder sweeps the <strong>national index</strong> LRP settles on. Your cattle sell at <strong>index {posBasisN >= 0 ? "+" : "−"} {fmt$(Math.abs(posBasisN))} basis</strong>. When the index is below your coverage, LRP pays <strong>(coverage − index) × cwt</strong>, netted against the sunk premium. Because LRP covers the index but you sell at cash, your true floor is <strong>coverage {posBasisN >= 0 ? "+" : "−"} {fmt$(Math.abs(posBasisN))} basis = {fmt$(posEffFloor)}/cwt</strong> — the basis gap is uninsured.
                </div>
              </div>

              {/* Settled actuals — real outcome for a closed endorsement */}
              {posActual && (
                <div style={{ background:"#eef4fb", border:"1px solid #b8d0ec", borderRadius:10, padding:"14px 16px", marginBottom:14 }}>
                  <div style={{ fontSize:12, fontWeight:800, color:"#1a4a7a", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>✔ This endorsement has settled — actual outcome</div>
                  <div style={{ fontSize:14, color:"#1a2e1a", lineHeight:1.6 }}>
                    Actual ending value <strong style={{ fontVariantNumeric: "tabular-nums" }}>{fmt$(posActual.aev)}/cwt</strong> vs your <strong>{fmt$(posCov)}</strong> floor →
                    LRP paid <strong style={{ fontVariantNumeric: "tabular-nums", color: posActual.indemTot > 0 ? "#1f6f3a" : "#7a7a7a" }}>{fmt$0(posActual.indemTot)}</strong> total
                    ({fmt$(posActual.indemHead)}/head). {posActual.indemTot > 0 ? "The floor was in the money." : "The market held above your floor, so no indemnity was due — the premium bought protection you didn't end up needing."}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Card style={{ textAlign:"center", padding:"30px 20px", border:"1px dashed #d8e0d0", color:"#60735f" }}>
              Enter your coverage price (floor), premium paid, sell weight, and a buy price above — or load one of your LRP endorsements — to see your position P&amp;L.
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ALERTS & SUBSCRIBERS MANAGER
// ─────────────────────────────────────────────────────────────────────────────
// Admin-only page (no public form). Two sections:
//   1. Email subscribers (LRP daily digest + DRP daily digest)
//   2. Coverage alerts — threshold-based triggers that fire emails
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PORTAL ADMIN — invite farmers / limited agents / MI viewers, assign per-farmer
// line-of-business visibility + Market Intelligence access, update, revoke.
// Admin-only screen. Talks to /api/portal-invite (service-role-backed, requireAdmin).
// The DB's is_admin() RLS gate is the real enforcement; this is the control surface.
// ─────────────────────────────────────────────────────────────────────────────
const PORTAL_LOBS = [
  { key: "spring", label: "Spring" },
  { key: "fall",   label: "Fall" },
  { key: "prf",    label: "PRF" },
  { key: "lrp",    label: "LRP" },
  { key: "drp",    label: "DRP" },
  { key: "grain",  label: "Grain" },  // farmer-editable grain marketing tool (opt-in, default off)
];
const ALL_LOB_KEYS = PORTAL_LOBS.map(l => l.key);
// Coverage lines a farm grant turns on by default. Grain is an interactive
// tool granted deliberately, so it's NOT in the default set (toggle it on).
const DEFAULT_GRANT_LOBS = ["spring", "fall", "prf", "lrp", "drp"];

// Shared editor for a user's grants (farmers × LOBs), MI flag, and role.
function PortalPermissionEditor({ farmersList, grants, setGrants, canViewMi, setCanViewMi, canViewElevator, setCanViewElevator, canSetAlerts, setCanSetAlerts, canView578, setCanView578, canViewModelFarm, setCanViewModelFarm, marketTabs, setMarketTabs, pageAccess, setPageAccess, canViewCashTargets, setCanViewCashTargets, grantLobs, role, setRole, isMobile }) {
  const [q, setQ] = useState("");
  const grantedIds = new Set(grants.map(g => g.farmer_id));
  const matches = q.trim().length >= 2
    ? farmersList.filter(f => (f.name || "").toLowerCase().includes(q.toLowerCase()) && !grantedIds.has(f.id)).slice(0, 8)
    : [];
  const nameFor = (id) => (farmersList.find(f => f.id === id)?.name) || id;
  const addFarmer = (f) => { setGrants([...grants, { farmer_id: f.id, lobs: [...DEFAULT_GRANT_LOBS] }]); setQ(""); };
  const removeFarmer = (id) => setGrants(grants.filter(g => g.farmer_id !== id));
  const toggleLob = (id, lob) => setGrants(grants.map(g => g.farmer_id === id
    ? { ...g, lobs: g.lobs.includes(lob) ? g.lobs.filter(l => l !== lob) : [...g.lobs, lob] }
    : g));

  // What "follow coverage" resolves to right now, from the LOBs ticked on this
  // form. Computed through marketHubTabs — the SAME rule the portal renders with
  // — so this can't drift from what the farmer gets. It reads the GRANT side
  // only: the portal additionally drops a coverage the farmer doesn't actually
  // carry, so the real list can be shorter for spring/fall/prf (never for
  // DRP/LRP, which markets show regardless).
  // Which farmer-scoped pages the grants on this form would already reach. The
  // picker greys out the rest, because page_access can only NARROW those — it
  // can never grant one, and an admin should not have to save to find that out.
  const grantReaches = useMemo(() => {
    const s = new Set();
    if ((grants || []).length) {
      ["home", "coverage", "claims", "fields", "yieldanalytics", "fieldprogress"].forEach(k => s.add(k));
      if (grantLobs.has("drp") || grantLobs.has("lrp")) s.add("quoters");
      if (grantLobs.has("prf")) { s.add("prfgrid"); s.add("prfreview"); }
      if (grantLobs.has("grain")) s.add("grain");
      if (role === "agent") s.add("fsa578");
    }
    if (canView578 || role === "underwriter") s.add("fsa578");
    if (canSetAlerts) s.add("alerts");
    return s;
  }, [grants, grantLobs, role, canView578, canSetAlerts]);
  const followingPages = !Array.isArray(pageAccess);
  // What "following their grants" resolves to right now, so both states show the
  // same ticks and switching between them is not a surprise.
  const derivedPages = useMemo(() => {
    const s = new Set(["agcalc", "rainwatch"]);   // ungated for every portal user
    grantReaches.forEach(k => s.add(k));
    if (canViewMi) s.add("market");
    if ((grants || []).length) s.add("markets");
    if (!(grants || []).length) s.add("briefing");
    return s;
  }, [grantReaches, canViewMi, grants]);
  const pageSetShown = followingPages ? derivedPages : new Set(pageAccess);
  const togglePage = (key) => {
    // First click off "follow their grants" freezes today's derived set and edits
    // from there — so switching ONE page off can never quietly grant the rest.
    const base = followingPages ? new Set(derivedPages) : new Set(pageAccess);
    if (base.has(key)) base.delete(key); else base.add(key);
    setPageAccess(PORTAL_PAGES.filter(p => base.has(p.key)).map(p => p.key));
  };

  const followTabs = marketHubTabs(grantLobs, null);
  const following = !Array.isArray(marketTabs);
  const shownTabs = following ? new Set(followTabs.map(t => t.key)) : new Set(marketTabs);
  const toggleMarketTab = (key) => {
    // First click off "follow coverage" freezes today's derived set, then edits
    // it — so unticking one market never silently grants the other six.
    const base = following ? new Set(followTabs.map(t => t.key)) : new Set(marketTabs);
    if (base.has(key)) base.delete(key); else base.add(key);
    setMarketTabs(MARKET_TABS.filter(t => base.has(t.key)).map(t => t.key));
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ display:"flex", gap:14, flexWrap:"wrap", alignItems:"center" }}>
        <label style={{ fontSize:12, fontWeight:700, color:"#2d4a2d" }}>Role:&nbsp;
          <select value={role} onChange={e=>setRole(e.target.value)}
            style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #c8d8c8", fontSize:12 }}>
            <option value="farmer">Farmer</option>
            <option value="agent">Limited agent</option>
            <option value="underwriter">Underwriter (full-book 578)</option>
            <option value="market">Market-intel only</option>
          </select>
        </label>
        <label style={{ fontSize:12, fontWeight:700, color:"#2d4a2d", display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}
          title="Standalone Market Intelligence page (grain basis, futures, fund positioning, elevator merchandiser). This is SEPARATE from the per-coverage market pages (Milk MI / Cattle MI / Grain MI / PRF Grid), which are unlocked automatically by the coverage grants below.">
          <input type="checkbox" checked={canViewMi} onChange={e=>setCanViewMi(e.target.checked)} />
          📈 Market Intelligence access
        </label>
        <label style={{ fontSize:12, fontWeight:700, color: canViewMi ? "#2d4a2d" : "#a8b8a8", display:"flex", alignItems:"center", gap:6, cursor: canViewMi ? "pointer" : "not-allowed" }}
          title="Additional access to the Elevator / grain-merchandiser tab (carry spreads, fund positioning). Requires Market Intelligence access.">
          <input type="checkbox" checked={canViewElevator} disabled={!canViewMi} onChange={e=>setCanViewElevator(e.target.checked)} />
          🏢 Elevator (merchandiser)
        </label>
        <label style={{ fontSize:12, fontWeight:700, color:"#2d4a2d", display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
          <input type="checkbox" checked={canSetAlerts} onChange={e=>setCanSetAlerts(e.target.checked)} />
          🔔 Price alerts
        </label>
        {/* AGENCY-WIDE, and labelled as such on the control itself. This is not a
            farm grant and not a market page: it is the whole book of working
            cash-target orders across EVERY grower, with their names on it. It
            gets its own switch rather than a tick in the page list precisely so
            it cannot be handed over while ticking pages — the same argument that
            keeps can_view_578 out of this row. */}
        <label style={{ fontSize:12, fontWeight:700, color:"#7a4a1a", display:"flex", alignItems:"center", gap:6, cursor:"pointer",
                        background:"#fdf7e8", border:"1px solid #e6d5a8", borderRadius:6, padding:"3px 8px" }}
          title="The agency's working-order book across EVERY grower, including their names. Grants read, write and close — not delete. Not a farm grant, and not reachable from the page list below.">
          <input type="checkbox" checked={canViewCashTargets} onChange={e=>setCanViewCashTargets(e.target.checked)} />
          🎯 Cash Targets (all growers)
        </label>
        {/* "578 documents (all farms)" and "Model Farm" are deliberately NOT
            offered here.
            578: the checkbox granted the ENTIRE book's files in one click, sat
            beside four harmless toggles, and was the flag that quietly handed a
            limited agent book-wide access. It is too consequential to be one
            mis-click away while assigning a farmer their own farm. The two
            people who need it (Mandy, Daniele) already have it; anyone else is
            a deliberate DB change.
            Model Farm: retired, and its tables are dropped in sql/014.
            Both pieces of STATE are still read on edit and sent on save, so
            hiding the inputs never silently clears an existing grant. */}
      </div>

      {role === "agent" && (
        <div style={{ fontSize:12, color:"#3a6e32", background:"#eef6ec", border:"1px solid #cfe6cf", borderRadius:6, padding:"8px 11px", lineHeight:1.5 }}>
          📋 As a <b>limited agent</b>, this user automatically sees <b>578 documents for their assigned farms only</b> — no checkbox needed.
          Book-wide 578 access is no longer grantable from this screen; it needs a deliberate change in the database.
        </div>
      )}

      <div style={{ fontSize:11.5, color:"#3a5a3a", background:"#f2f6f2", border:"1px solid #dbe6db", borderRadius:6, padding:"8px 11px", lineHeight:1.55 }}>
        Each coverage you enable on a farm drives that farm's <b>My Coverage</b> tab and also unlocks its tools:
        <b> DRP</b> & <b>LRP</b> → the matching <b>Quoter</b>; <b>DRP</b> → Milk MI · <b>LRP</b> → Cattle MI · <b>Spring/Fall</b> → Grain MI on the <b>Markets</b> page.
        <b>PRF</b> is the exception — it opens its own <b>PRF Grid</b> page, not a Markets tab, so the Markets
        checkboxes below cannot turn it on or off. Only the PRF coverage on a farm can.
        Turn a coverage off and its quoter/market tab disappears for that user.
      </div>

      {/* ── Markets this user sees ───────────────────────────────────────────
          Market pages are NOT coverage. A grower who carries no DRP can still
          usefully read the milk market, and one who carries wheat may not want
          six dairy tabs. Coverage drives the default; this overrides it.

          Two states, deliberately distinguishable and NOT a tri-state checkbox:
            • "Follow coverage" (stored NULL) — what every user had before this
              existed, so nobody's page changed when it shipped, and their
              markets keep tracking their coverage as policies come and go.
            • an explicit list — frozen, and only what is ticked.
          Ticking any box the first time freezes today's derived set and edits
          from there, so switching ONE market off can never quietly grant the
          rest. Unticking everything is legal and means no market pages. */}
      <div style={{ border:"1px solid #dbe6db", borderRadius:8, padding:"11px 13px", background:"#fff" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:9 }}>
          <div style={{ fontSize:11, fontWeight:800, letterSpacing:"0.08em", color:"#5a6a7a", textTransform:"uppercase" }}>
            Markets this user sees
          </div>
          {following
            ? <span style={{ fontSize:10.5, fontWeight:700, color:"#2d6a2d", background:"#eaf3ea", borderRadius:11, padding:"2px 9px" }}>following their coverage</span>
            : <button type="button" onClick={()=>setMarketTabs(null)}
                style={{ fontSize:10.5, fontWeight:700, color:"#1a4a7a", background:"none", border:"none", textDecoration:"underline", cursor:"pointer", padding:0 }}>
                ↺ follow their coverage instead
              </button>}
        </div>
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap:"6px 12px" }}>
          {MARKET_TABS.map(t => {
            const on = shownTabs.has(t.key);
            // A market OUTSIDE the coverage-derived set is still selectable —
            // that is the whole point — but it's worth showing which ones are
            // being added beyond what their coverage would give them.
            const beyond = !followTabs.some(f => f.key === t.key);
            return (
              <label key={t.key} title={beyond ? "Not implied by their coverage — turning this on shows it anyway." : "Implied by their coverage."}
                style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:on?700:500, color:on?"#1a2e1a":"#60735f", cursor:"pointer" }}>
                <input type="checkbox" checked={on} onChange={()=>toggleMarketTab(t.key)} />
                <span>{t.icon} {t.label}</span>
                {on && beyond && <span style={{ fontSize:10, fontWeight:800, color:"#8a6d1f", background:"#fdf7e8", borderRadius:9, padding:"1px 5px" }}>+</span>}
              </label>
            );
          })}
        </div>
        {!following && shownTabs.size === 0 && (
          <div style={{ fontSize:11, color:"#8a6d1f", background:"#fdf7e8", border:"1px solid #e6d5a8", borderRadius:6, padding:"6px 9px", marginTop:9 }}>
            No markets selected — the <b>Markets</b> page will not appear for this user at all.
          </div>
        )}
      </div>

      {/* ── Pages this user sees ─────────────────────────────────────────────
          The page allowlist (sql/049). Same three states as Markets above, and
          the same reason they are kept apart: "follow their grants" (NULL) is
          what everyone has today, and it must stay distinguishable from an
          explicit list so a plain re-save never freezes somebody's nav.

          THE PICKER SHOWS WHAT AN ALLOWLIST CAN AND CANNOT DO, rather than
          letting an admin discover it by saving. A farmer-scoped page is
          disabled unless the grants below already reach it, because ticking it
          could only ever have LOOKED like it worked — page_access narrows those,
          it never grants them. That is the Stein Family Farms defect (a tick
          that stored, was overruled, and said nothing) refused up front rather
          than repeated one level up.

          Cash Targets is deliberately NOT in this list — it is agency-wide and
          rides its own switch above. */}
      <div style={{ border:"1px solid #dbe6db", borderRadius:8, padding:"11px 13px", background:"#fff" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:9 }}>
          <div style={{ fontSize:11, fontWeight:800, letterSpacing:"0.08em", color:"#5a6a7a", textTransform:"uppercase" }}>
            Pages this user sees
          </div>
          {followingPages
            ? <span style={{ fontSize:10.5, fontWeight:700, color:"#2d6a2d", background:"#eaf3ea", borderRadius:11, padding:"2px 9px" }}>following their grants</span>
            : <button type="button" onClick={()=>setPageAccess(null)}
                style={{ fontSize:10.5, fontWeight:700, color:"#1a4a7a", background:"none", border:"none", textDecoration:"underline", cursor:"pointer", padding:0 }}>
                ↺ follow their grants instead
              </button>}
        </div>
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap:"6px 12px" }}>
          {PORTAL_PAGES.filter(p => p.cls !== "agency").map(p => {
            const reachable = p.cls === "open" || grantReaches.has(p.key);
            const on = pageSetShown.has(p.key) && reachable;
            return (
              <label key={p.key}
                title={p.cls === "open"
                  ? "Reads no customer data — safe to grant to anyone."
                  : reachable ? "Customer data — their farm grants already reach this page."
                  : "Customer data. Grant them a farm below before this page can be turned on."}
                style={{ display:"flex", alignItems:"center", gap:6, fontSize:12,
                         fontWeight:on?700:500,
                         color: !reachable ? "#b0bcb0" : (on?"#1a2e1a":"#60735f"),
                         cursor: reachable ? "pointer" : "not-allowed" }}>
                <input type="checkbox" checked={on} disabled={!reachable}
                  onChange={()=>togglePage(p.key)} />
                <span>{p.label}</span>
                {p.cls === "open" && <span title="Reads no customer data"
                  style={{ fontSize:10, fontWeight:800, color:"#2d6a2d", background:"#eaf3ea", borderRadius:9, padding:"1px 5px" }}>safe</span>}
              </label>
            );
          })}
        </div>
        {!followingPages && pageSetShown.size === 0 && (
          <div style={{ fontSize:11, color:"#8a6d1f", background:"#fdf7e8", border:"1px solid #e6d5a8", borderRadius:6, padding:"6px 9px", marginTop:9 }}>
            No pages selected — this user will reach nothing at all.
          </div>
        )}
      </div>

      <div style={{ position:"relative" }}>
        <div style={{ fontSize:11, fontWeight:800, letterSpacing:"0.08em", color:"#5a6a7a", textTransform:"uppercase", marginBottom:5 }}>
          Farms this user can see
        </div>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Type 2+ letters to find a farm…"
          style={{ width:"100%", maxWidth:380, padding:"7px 10px", borderRadius:6, border:"1px solid #c8d8c8", fontSize:13, boxSizing:"border-box" }} />
        {matches.length > 0 && (
          <div style={{ position:"absolute", zIndex:20, background:"#fff", border:"1px solid #c8d8c8", borderRadius:6, marginTop:2, maxWidth:380, width:"100%", boxShadow:"0 6px 18px rgba(0,0,0,0.12)" }}>
            {matches.map(f => (
              <div key={f.id} onClick={()=>addFarmer(f)}
                style={{ padding:"7px 10px", fontSize:13, cursor:"pointer", borderBottom:"1px solid #eef2ee" }}
                onMouseEnter={e=>e.currentTarget.style.background="#f2f6f2"}
                onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                {f.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {grants.length === 0 && (
        <div style={{ fontSize:12, color:"#9a8060", fontStyle:"italic" }}>
          No farms assigned yet. {(canViewMi || canView578 || canSetAlerts)
            ? `User will see only: ${[canView578 && "578 documents", canViewMi && "Market Intelligence", canSetAlerts && "Price alerts"].filter(Boolean).join(", ")}.`
            : "User will have no data until you add a farm or enable an access toggle above."}
        </div>
      )}
      {grants.map(g => (
        <div key={g.farmer_id} style={{ border:"1px solid #d8e0d8", borderRadius:8, padding:"10px 12px", background:"#fafbfa" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontWeight:800, fontSize:13, color:"#1a2e1a" }}>{nameFor(g.farmer_id)}</span>
            <button onClick={()=>removeFarmer(g.farmer_id)}
              style={{ background:"none", border:"none", color:"#c0392b", fontSize:12, fontWeight:700, cursor:"pointer" }}>Remove</button>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {PORTAL_LOBS.map(lob => {
              const on = g.lobs.includes(lob.key);
              return (
                <button key={lob.key} onClick={()=>toggleLob(g.farmer_id, lob.key)}
                  style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer",
                    border:`1px solid ${on?"#2d6a2d":"#c8d8c8"}`, background:on?"#2d6a2d":"#fff", color:on?"#fff":"#60735f" }}>
                  {on ? "✓ " : ""}{lob.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PORTAL SYSTEM PANEL — kill switches + crash reports
// ─────────────────────────────────────────────────────────────────────────────
// Two switches, deliberately different in weight, because they answer different
// questions:
//
//   "We shipped a bug / I'm mid-migration"  → Maintenance mode (soft).
//        Farmers see a maintenance card. Grants untouched. One click, reversible.
//        A UI gate only — a live JWT could still read data via the API directly.
//
//   "I think data may be exposed"           → Revoke all access (hard).
//        Flips every is_active flag off at the DB after snapshotting them. All 55
//        portal RLS policies gate on those flags, so a stale JWT gets zero rows.
//        Restore replays the snapshot.
//
// The hard switch is styled as a danger zone and requires typing REVOKE, because
// the failure mode of an accidental click is every farmer locked out at once.
function PortalSystemPanel({ isMobile = false }) {
  const [cfg, setCfg]         = useState(null);
  const [snap, setSnap]       = useState(null);   // most recent un-restored snapshot
  const [errors, setErrors]   = useState([]);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [showDanger, setShowDanger]   = useState(false);
  const [showErrors, setShowErrors]   = useState(false);

  async function load() {
    setErr(null);
    try {
      const headers = getAuthHeaders();
      const [cRes, sRes, eRes] = await Promise.all([
        fetch(`${SUPABASE_URL}/rest/v1/app_config?select=*&id=eq.1`, { headers }),
        fetch(`${SUPABASE_URL}/rest/v1/portal_access_snapshots?select=id,taken_at,taken_by,reason&restored_at=is.null&order=taken_at.desc&limit=1`, { headers }),
        fetch(`${SUPABASE_URL}/rest/v1/client_errors?select=*&resolved=eq.false&order=occurred_at.desc&limit=50`, { headers }),
      ]);
      setCfg(cRes.ok ? ((await cRes.json())[0] || null) : null);
      setSnap(sRes.ok ? ((await sRes.json())[0] || null) : null);
      setErrors(eRes.ok ? await eRes.json() : []);
    } catch (e) { setErr(String(e.message || e)); }
  }
  useEffect(() => { load(); }, []);

  async function setPortalEnabled(enabled) {
    const message = enabled ? null : (window.prompt(
      "Message to show farmers while the portal is down (optional):",
      "We’re making some updates and the portal is briefly unavailable. Please check back shortly — your coverage is unaffected."
    ));
    if (!enabled && message === null) return;   // cancelled the prompt
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/app_config?id=eq.1`, {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json", Prefer: "return=representation" },
        body: JSON.stringify({
          portal_enabled: enabled,
          portal_message: enabled ? null : (message || null),
          updated_at: new Date().toISOString(),
          updated_by: getCurrentUserEmail() || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  async function rpc(fn, body) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
      method: "POST",
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function revokeAll() {
    if (confirmText !== "REVOKE") return;
    const reason = window.prompt("Why are you revoking all portal access? (recorded on the snapshot)") || null;
    setBusy(true); setErr(null);
    try {
      await rpc("portal_revoke_all", { p_reason: reason });
      setConfirmText(""); setShowDanger(false);
      await load();
      alert("All portal access revoked and maintenance mode enabled. Prior access was snapshotted — use Restore to undo.");
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  // The middle tier. Pausing is a UI gate that a device only notices when it
  // re-checks; ending the sessions is what boots a phone that's already open,
  // without touching a single grant. Admins are excluded server-side, so this
  // can't sign you out mid-incident.
  async function signOutAll() {
    if (!window.confirm(
      "Sign every portal user out on every device?\n\n" +
      "Nobody loses access — grants are untouched. They'll simply have to sign in again, " +
      "and if the portal is paused they'll land on the maintenance card.\n\n" +
      "You and the other admins stay signed in."
    )) return;
    const reason = window.prompt("Why? (recorded in the portal log)") || null;
    setBusy(true); setErr(null);
    try {
      const n = await rpc("portal_signout_all", { p_reason: reason });
      alert(`Signed out ${n} session${n === 1 ? "" : "s"}. Anyone with the app open drops to the sign-in screen on their next request.`);
      await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  async function restore() {
    if (!window.confirm("Restore portal access to the state captured in the snapshot, and re-enable the portal?")) return;
    setBusy(true); setErr(null);
    try {
      await rpc("portal_restore_snapshot", {});
      await load();
      alert("Access restored and the portal is live again.");
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  // Resolve every row behind a card, not just the one row we happened to render.
  // The list is grouped by fingerprint, so resolving the representative left its
  // four siblings unresolved and the same crash reappeared on the next refresh —
  // which is part of why six of these had been sitting here for two weeks after
  // the code was fixed.
  async function resolveErrors(ids) {
    const list = (ids || []).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/client_errors?id=in.(${list.join(",")})`, {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ resolved: true }),
      });
      await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  const live = cfg ? cfg.portal_enabled !== false : true;
  const card = { border:"1px solid #d8e0d8", borderRadius:10, padding:isMobile?"14px":"16px 18px", background:"#fff", marginBottom:14 };
  const btn  = (bg) => ({ padding:"8px 16px", background:bg, color:"#fff", border:"none", borderRadius:7, fontWeight:800, fontSize:13, cursor:"pointer" });

  // Group identical crashes so one runaway bug doesn't read as 40 problems, and
  // keep every row's id on the group so "Mark resolved" clears the whole thing.
  const grouped = [];
  const seen = new Map();
  errors.forEach(e => {
    const k = e.fingerprint || e.message;
    if (seen.has(k)) { const g = seen.get(k); g.count++; g.ids.push(e.id); return; }
    const row = { ...e, count: 1, ids: [e.id] };
    seen.set(k, row); grouped.push(row);
  });
  // A crash reported against a build that is no longer deployed says nothing about
  // what is live — the fix usually shipped in the very next commit. Counting those
  // in the headline is how the panel came to show "6 unresolved crashes" when all
  // six had been fixed days earlier. They stay visible, just separated and quiet.
  const isLiveBuild = (e) => !e.app_version || e.app_version === APP_VERSION;
  const liveCrashes = grouped.filter(isLiveBuild);
  const oldCrashes  = grouped.filter(e => !isLiveBuild(e));

  return (
    <div style={{ marginBottom: 26 }}>
      {err && <div style={{ background:"#fdecea", color:"#c0392b", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:12 }}>{err}</div>}

      {/* ── Status + maintenance toggle ── */}
      <div style={{ ...card, borderLeft:`4px solid ${live ? "#2d6a2d" : "#e67e22"}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <div>
            <div style={{ fontWeight:800, fontSize:14, color:"#1a2e1a", marginBottom:3 }}>
              {live ? "🟢 Portal is live" : "🟠 Portal is in maintenance mode"}
            </div>
            <div style={{ fontSize:12, color:"#5a6a5a" }}>
              {live
                ? "Farmers can sign in and see their data."
                : `Farmers see a maintenance card. ${cfg?.updated_by ? `Set by ${cfg.updated_by}.` : ""}`}
            </div>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <button disabled={busy} onClick={signOutAll}
              title="End every portal user's session on every device. Grants are untouched — they just have to sign in again. Admins are not signed out."
              style={{ ...btn("#7a5a00"), opacity:busy?0.6:1 }}>
              Sign everyone out
            </button>
            <button disabled={busy} onClick={()=>setPortalEnabled(!live)}
              style={{ ...btn(live ? "#e67e22" : "#2d6a2d"), opacity:busy?0.6:1 }}>
              {live ? "Pause portal" : "Bring portal back"}
            </button>
          </div>
        </div>
        {!live && cfg?.portal_message && (
          <div style={{ marginTop:10, fontSize:12, color:"#5a6a5a", background:"#fdf6ec", border:"1px solid #f0d9b5", borderRadius:6, padding:"8px 10px" }}>
            Showing: “{cfg.portal_message}”
          </div>
        )}
        {/* How long a pause takes to bite. This was the surprise on 2026-08-03:
            the switch was flipped and phones that were already open kept going. */}
        <div style={{ marginTop:10, fontSize:11.5, color:"#6a7a6a", lineHeight:1.5 }}>
          {live
            ? <>Pausing shows farmers a maintenance card. Devices already open pick it up when the app is next opened, or within 2 minutes if it’s in front of them.</>
            : <>Anyone who already had the app open picks this up when they next open it, or within 2 minutes if it’s on screen. To boot them <b>immediately</b>, use <b>Sign everyone out</b>.</>}
          {" "}Neither one changes anyone’s access — for that, see Revoke below.
        </div>
      </div>

      {/* ── Crash reports ── */}
      <div style={card}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <div>
            <div style={{ fontWeight:800, fontSize:14, color:"#1a2e1a", marginBottom:3 }}>
              {liveCrashes.length === 0 ? "✅ No unresolved crashes on this build" : `⚠️ ${liveCrashes.length} unresolved crash${liveCrashes.length===1?"":"es"}`}
            </div>
            <div style={{ fontSize:12, color:"#5a6a5a" }}>
              Reported automatically from users’ browsers. Grouped by cause.
              {oldCrashes.length > 0 && ` ${oldCrashes.length} more came from an earlier build and may already be fixed.`}
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={load} style={{ ...btn("#5a6a5a"), padding:"7px 12px", fontSize:12 }}>Refresh</button>
            {oldCrashes.length > 0 && (
              <button disabled={busy}
                title="Clear the crashes reported against builds that are no longer deployed. Check them first — this only says they aren't happening on what's live."
                onClick={()=>{ if (window.confirm(`Mark ${oldCrashes.length} crash group${oldCrashes.length===1?"":"s"} from earlier builds as resolved?`)) resolveErrors(oldCrashes.flatMap(e=>e.ids)); }}
                style={{ ...btn("#60735f"), padding:"7px 12px", fontSize:12, opacity:busy?0.6:1 }}>
                Clear {oldCrashes.length} old
              </button>
            )}
            {grouped.length > 0 && (
              <button onClick={()=>setShowErrors(v=>!v)} style={{ ...btn("#2d4a2d"), padding:"7px 12px", fontSize:12 }}>
                {showErrors ? "Hide" : "View"}
              </button>
            )}
          </div>
        </div>

        {showErrors && [...liveCrashes, ...oldCrashes].map(e => (
          <div key={e.id} style={{ marginTop:10, border:"1px solid #e8e0e0", borderRadius:8, padding:"10px 12px",
                                   background: isLiveBuild(e) ? "#fffafa" : "#fafafa", opacity: isLiveBuild(e) ? 1 : 0.75 }}>
            <div style={{ display:"flex", justifyContent:"space-between", gap:10, flexWrap:"wrap", marginBottom:5 }}>
              <span style={{ fontWeight:800, fontSize:12, color: isLiveBuild(e) ? "#c0392b" : "#7a6a6a", wordBreak:"break-word" }}>
                {e.message}{e.count > 1 && <span style={{ color:"#60735f", fontWeight:600 }}> ×{e.count}</span>}
                {!isLiveBuild(e) && <span style={{ color:"#60735f", fontWeight:600 }}> · earlier build</span>}
              </span>
              <button onClick={()=>resolveErrors(e.ids)} disabled={busy}
                style={{ background:"none", border:"none", color:"#2d6a2d", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                Mark resolved
              </button>
            </div>
            <div style={{ fontSize:11, color:"#60735f", display:"flex", gap:10, flexWrap:"wrap" }}>
              <span>{new Date(e.occurred_at).toLocaleString()}</span>
              <span>· {e.surface || "?"}</span>
              <span>· {e.email || e.user_id}</span>
              {e.app_version && <span>· build {e.app_version}</span>}
              {e.kind && <span>· {e.kind}</span>}
            </div>
            {e.url && <div style={{ fontSize:11, color:"#60735f", marginTop:3, wordBreak:"break-all" }}>{e.url}</div>}
            {e.stack && (
              <details style={{ marginTop:6 }}>
                <summary style={{ fontSize:11, color:"#5a6a5a", cursor:"pointer" }}>Stack</summary>
                <pre style={{ fontSize:10, color:"#888", background:"#f8f8f8", border:"1px solid #ddd", borderRadius:6,
                  padding:"8px 10px", maxHeight:200, overflow:"auto", whiteSpace:"pre-wrap", wordBreak:"break-all", margin:"6px 0 0" }}>
                  {e.stack}{e.component_stack ? `\n\n--- component stack ---\n${e.component_stack}` : ""}
                </pre>
              </details>
            )}
          </div>
        ))}
      </div>

      {/* ── Danger zone ── */}
      <div style={{ ...card, borderColor:"#e8c8c8", background:"#fffafa" }}>
        {snap ? (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <div>
              <div style={{ fontWeight:800, fontSize:14, color:"#c0392b", marginBottom:3 }}>🔒 All portal access is revoked</div>
              <div style={{ fontSize:12, color:"#5a6a5a" }}>
                Revoked {new Date(snap.taken_at).toLocaleString()} by {snap.taken_by || "?"}
                {snap.reason ? ` — “${snap.reason}”` : ""}. Prior access is saved and restorable.
              </div>
            </div>
            <button disabled={busy} onClick={restore} style={{ ...btn("#2d6a2d"), opacity:busy?0.6:1 }}>Restore access</button>
          </div>
        ) : (
          <>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
              <div>
                <div style={{ fontWeight:800, fontSize:14, color:"#8a3a2a", marginBottom:3 }}>Danger zone — revoke all access</div>
                <div style={{ fontSize:12, color:"#5a6a5a", maxWidth:520, lineHeight:1.5 }}>
                  For a suspected data-exposure issue only. Cuts every portal user off at the database,
                  so even an already-signed-in session sees nothing. Pausing the portal above is the
                  right tool for an ordinary bug.
                </div>
              </div>
              {!showDanger && (
                <button onClick={()=>setShowDanger(true)}
                  style={{ padding:"8px 16px", background:"#fff", color:"#c0392b", border:"1px solid #e0b0b0", borderRadius:7, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                  Revoke all…
                </button>
              )}
            </div>
            {showDanger && (
              <div style={{ marginTop:12, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                <span style={{ fontSize:12, color:"#5a6a5a" }}>Type <b>REVOKE</b> to confirm:</span>
                <input value={confirmText} onChange={e=>setConfirmText(e.target.value)} placeholder="REVOKE"
                  style={{ padding:"7px 10px", borderRadius:6, border:"1px solid #e0b0b0", fontSize:13, width:120 }} />
                <button disabled={busy || confirmText !== "REVOKE"} onClick={revokeAll}
                  style={{ ...btn("#c0392b"), opacity:(busy || confirmText !== "REVOKE") ? 0.45 : 1, cursor:(confirmText !== "REVOKE") ? "not-allowed" : "pointer" }}>
                  Revoke all access
                </button>
                <button onClick={()=>{ setShowDanger(false); setConfirmText(""); }}
                  style={{ padding:"8px 14px", background:"#fff", color:"#5a6a5a", border:"1px solid #d8e0d8", borderRadius:7, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// USAGE CHART PRIMITIVES
//
// Small inline-SVG pieces for the Portal → Usage tab. No chart library — the
// repo loads D3 from a CDN for the heavy pages, and none of this needs it.
//
// Color decisions here are computed, not eyeballed (dataviz validate_palette.js):
//   • UT.ramp is an ORDINAL/SEQUENTIAL ramp — one hue, monotone lightness. It
//     passes monotone-L, adjacent ΔL ≥ 0.06, single-hue (4° spread) and
//     light-end contrast 2.03:1 on white. Used for funnel stages (order IS the
//     meaning), the hour heatmap (magnitude), and depth buckets (ordered).
//   • Nominal lists (pages, actions, sources) all take ONE hue — bar length
//     already encodes the value, so spending the identity channel on it would
//     be re-encoding.
//   • UT.mute #657565 (4.90:1) and UT.warn #8a5e10 (5.69:1) clear 4.5:1 for
//     small text. The app's usual #677167 is 2.49:1 and #b07a20 is 3.72:1 —
//     both fail at the 10–12px this panel uses, so they're not used here.
// ─────────────────────────────────────────────────────────────────────────────
const UT = {
  ink:  "#1a2e1a",
  body: "#2d4a2d",
  mute: "#657565",
  warn: "#8a5e10",
  // A page that FAILED is a different status from a page that is merely empty,
  // and the Dead ends panel has to separate them at a glance. 7.3:1 on white, so
  // it clears 4.5:1 at the 11px that panel uses — the app's usual #a33 does not.
  bad:  "#9a3412",
  ramp: ["#8ec482", "#63ab5c", "#41903f", "#296528", "#173d17"],
};

// Gap-fill a daily rollup so quiet days are zeros, not missing points. Returns
// oldest→newest, which is the direction every chart below reads.
function usageFillDays(daily, n) {
  const by = {};
  (daily || []).forEach(d => { by[d.day] = d; });
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const dt = new Date(Date.now() - i * 86400000);
    const key = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
    const r = by[key];
    out.push({
      day: key,
      sessions: Number(r?.sessions || 0),
      users: Number(r?.users || 0),
      screen_views: Number(r?.screen_views || 0),
      actions: Number(r?.actions || 0),
    });
  }
  return out;
}

// Column chart with a soft area behind it. Bars (not a bare line) because daily
// session counts are small integers — a line between 0 and 2 implies a
// continuum that isn't there.
function UsageArea({ data = [], height = 104 }) {
  const max = Math.max(1, ...data.map(d => d.sessions));
  const W = 720, H = height, pad = 2;
  const bw = W / data.length;
  const fmtDay = (k) => {
    const [y, m, d] = k.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width:"100%", height, display:"block" }} role="img"
           aria-label={`Daily sessions, ${data.length} days, peak ${max}`}>
        <defs>
          <linearGradient id="usageAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={UT.ramp[2]} stopOpacity="0.9" />
            <stop offset="100%" stopColor={UT.ramp[2]} stopOpacity="0.55" />
          </linearGradient>
        </defs>
        {/* recessive baseline */}
        <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="#e0e6e0" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {data.map((d, i) => {
          const h = d.sessions === 0 ? 0 : Math.max(3, (d.sessions / max) * (H - 6));
          return (
            <g key={d.day}>
              <rect x={i * bw + pad / 2} y={H - h} width={Math.max(1, bw - pad)} height={h}
                    fill="url(#usageAreaFill)" rx="1.5" />
              {/* full-height hit target so thin/zero bars are still hoverable */}
              <rect x={i * bw} y="0" width={bw} height={H} fill="transparent">
                <title>{`${fmtDay(d.day)} — ${d.sessions} session${d.sessions === 1 ? "" : "s"}, ${d.users} user${d.users === 1 ? "" : "s"}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:UT.mute, marginTop:5 }}>
        <span>{data.length ? fmtDay(data[0].day) : ""}</span>
        <span>{data.length ? fmtDay(data[data.length - 1].day) : ""}</span>
      </div>
    </div>
  );
}

// Tiny trend line for a stat tile. No axes, no labels — the tile's number is the
// value; this only shows shape.
function UsageSparkline({ values = [], width = 120, height = 26 }) {
  if (!values.length) return null;
  const max = Math.max(1, ...values);
  const step = width / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => `${(i * step).toFixed(1)},${(height - (v / max) * (height - 3) - 1.5).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width:"100%", height, display:"block" }} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={UT.ramp[2]} strokeWidth="2"
                strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// Change vs the previous equal-length window. More usage is the good direction
// here, so it wears status semantics — always with an arrow glyph + words, never
// color alone.
function UsageDelta({ now, prev, days }) {
  const n = Number(now) || 0, p = Number(prev) || 0;
  if (!p && !n) return null;
  const diff = n - p;
  const pct = p ? Math.round(diff / p * 100) : null;
  const flat = diff === 0;
  const color = flat ? UT.mute : diff > 0 ? UT.ramp[3] : UT.warn;
  return (
    <div style={{ fontSize:10.5, color, marginTop:6, fontWeight:700 }}>
      {flat ? "→" : diff > 0 ? "▲" : "▼"} {flat ? "no change" : `${diff > 0 ? "+" : ""}${pct == null ? diff : `${pct}%`}`}
      <span style={{ color:UT.mute, fontWeight:500 }}> vs prior {days}d</span>
    </div>
  );
}

// A single ratio against a limit — a meter, not a two-slice pie.
function UsageMeter({ value, total, label }) {
  const pct = total ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div style={{ marginTop:8 }}>
      <div style={{ height:7, background:"#f2f6f2", borderRadius:4, overflow:"hidden" }}>
        <div style={{ width:`${Math.max(2, pct)}%`, height:"100%", background:UT.ramp[3] }} />
      </div>
      {label && <div style={{ fontSize:10.5, color:UT.mute, marginTop:5, lineHeight:1.4 }}>{label}</div>}
    </div>
  );
}

// Labeled bar row for nominal lists (pages, actions, sources). One hue by
// design — the bar length is the value, so color would be re-encoding it.
function UsageRow({ rank, label, value, suffix, max, color }) {
  return (
    <div style={{ marginBottom:9 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8, marginBottom:3 }}>
        <span style={{ fontSize:12, fontWeight:700, color:UT.body, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
          {rank != null && <span style={{ color:UT.mute, fontWeight:800, marginRight:6 }}>{rank}</span>}
          {label}
        </span>
        <span style={{ fontSize:11, color:UT.mute, whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums" }}>
          <b style={{ color:UT.ink, fontSize:12 }}>{Number(value).toLocaleString()}</b>{suffix ? ` · ${suffix}` : ""}
        </span>
      </div>
      <div style={{ height:8, background:"#f2f6f2", borderRadius:4, overflow:"hidden" }}>
        <div title={`${label}: ${value}`}
             style={{ width:`${Math.max(2, (Number(value) / Math.max(1, max)) * 100)}%`, height:"100%", background:color || UT.ramp[3] }} />
      </div>
    </div>
  );
}

// Empty states carry weight on this page: most panels start empty and stay that
// way for days. "Nothing yet, and here's why" reads as working; a bare grey
// sentence reads as broken.
function UsageEmpty({ icon, title, note, compact = false }) {
  return (
    <div style={{ border:"1px dashed #d8e0d8", borderRadius:9, background:"#fafcf9",
                  padding: compact ? "16px 14px" : "26px 18px", textAlign:"center", marginTop:10 }}>
      <div style={{ fontSize: compact ? 19 : 25, marginBottom:5, opacity:0.75 }}>{icon}</div>
      <div style={{ fontSize:12.5, fontWeight:700, color:UT.body }}>{title}</div>
      {note && <div style={{ fontSize:11.5, color:UT.mute, marginTop:4, maxWidth:340, marginLeft:"auto", marginRight:"auto", lineHeight:1.5 }}>{note}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PORTAL USAGE — the adoption/engagement read on the farmer portal.
//
// Sources, and what each one can and can't tell you:
//   • portal_users (via /api/portal-invite) — who was INVITED, and (from
//     auth.users) who has ever SIGNED IN. This is the denominator.
//   • portal_installs — who saved it to a phone home screen.
//   • portal_events + its rollup views — what people actually DO once inside:
//     sessions, minutes, which screens, which actions. Started 2026-07-29, so
//     "no activity" on a user who signed in before that date means "before we
//     were counting", not "never used it". The banner says so until the log
//     is older than the oldest login.
//
// Minutes are session first-seen → last-seen, with heartbeats only while the
// tab is visible — so it approximates attention, not tab-open time.
// ─────────────────────────────────────────────────────────────────────────────
const PORTAL_SCREEN_LABELS = {
  home:"🏠 Home", spring:"🌱 Spring Crops", fall:"🌾 Fall Crops", drp:"🥛 DRP",
  lrp:"🐄 LRP", prf:"🌧️ PRF", claims:"💰 Claims", fields:"🌾 Field Mapper",
  grain:"🌽 Grain Marketing", alerts:"🔔 Price Alerts", fsa578:"📋 578 Documents",
  market:"📈 Market Intelligence",
  // The nav keys added after this map was written. An unlabelled key renders as
  // the raw slug — "yieldanalytics" sat 4th on Most-used pages, and the same map
  // names the rows in Dead ends, where a raw slug is one more thing between you
  // and the customer to call. Keys and icons taken from portalNav.
  coverage:"📇 My Coverage", quoters:"🧮 Quoters", markets:"📊 Markets",
  prfgrid:"🌧️ PRF Grid",
  yieldanalytics:"🎯 Yield Analytics", fieldprogress:"🌱 Field Progress",
  agcalc:"📐 Ag Calculators", rainwatch:"🌦️ Weather",
  // Retired 2026-07-29. Kept so historical portal_events rows still label correctly.
  modelfarm:"🚜 Model Farm",
};
// Friction, not engagement — these three are the Dead ends panel's material and
// must stay OUT of "What they did", where a failed load would read as something
// the farmer chose to do. One set, because the filter is written twice.
const FRICTION_ACTIONS = new Set(["empty_state", "blocked_nav", "load_error"]);
const PORTAL_ACTION_LABELS = {
  open_farm:"Opened a farm", set_alert:"Created / edited a price alert",
  open_document:"Opened a document", save_grain_contract:"Saved a grain contract",
};

function PortalUsageView({ users = [], isMobile = false }) {
  const [rows, setRows] = useState(null);       // per-user rollup
  const [screens, setScreens] = useState([]);
  const [daily, setDaily] = useState([]);
  const [actions, setActions] = useState([]);
  const [lastActive, setLastActive] = useState({});  // user_id → auth session recency
  const [sessions, setSessions] = useState([]);
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const since = new Date(Date.now() - 90 * 86400000).toISOString();
        const [uu, sc, dl, ac, ss, hh, laRes] = await Promise.all([
          sbFetch("portal_user_usage"),
          sbFetch("portal_screen_usage"),
          sbFetch("portal_usage_daily", { order: "day.desc" }),
          // Actions AND session_start in one pull: session_start is where the
          // ?src= email-attribution tag lands.
          sbFetch("portal_events", {
            select: "event_type,action,screen,detail,occurred_at,user_id",
            filters: ["event_type=in.(action,session_start)", `occurred_at=gte.${since}`],
          }),
          sbFetch("portal_sessions"),
          sbFetch("portal_activity_hours"),
          // Real recency. See the note on portal_last_active() — last_sign_in_at
          // only moves on a fresh password sign-in, so it badly understates
          // anyone who stays signed in.
          fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_last_active`, { method: "POST", headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, body: "{}" }),
        ]);
        if (!alive) return;
        const la = {};
        if (laRes.ok) (await laRes.json()).forEach(r => { la[r.user_id] = r.last_active_at; });
        setRows(uu); setScreens(sc); setDaily(dl); setActions(ac);
        setSessions(ss); setHours(hh); setLastActive(la);
      } catch (e) { if (alive) setErr(String(e.message || e)); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const usageById = useMemo(() => {
    const m = {};
    (rows || []).forEach(r => { m[r.user_id] = r; });
    return m;
  }, [rows]);

  // Recency precedence, best signal first:
  //   1. portal_events.last_seen  — real in-app activity (only from 2026-07-29)
  //   2. auth session refresh     — proves they OPENED the app, works historically
  //   3. auth.users.last_sign_in_at — only a fresh password login; weakest
  const seenOf = (u) => usageById[u.user_id]?.last_seen || lastActive[u.user_id] || u.last_sign_in_at || null;

  const cutoff = Date.now() - days * 86400000;
  const active = users.filter(u => u.is_active !== false);
  const everIn = active.filter(u => seenOf(u));
  const recent = active.filter(u => { const s = seenOf(u); return s && new Date(s).getTime() > cutoff; });
  const weekly = active.filter(u => { const s = seenOf(u); return s && new Date(s).getTime() > Date.now() - 7 * 86400000; });
  const installed = active.filter(u => u.installed_at);

  // Window-scoped totals straight off the daily rollup.
  const win = daily.filter(d => new Date(d.day + "T12:00:00").getTime() > cutoff);
  const sessionsWin = win.reduce((s, d) => s + Number(d.sessions || 0), 0);
  const viewsWin    = win.reduce((s, d) => s + Number(d.screen_views || 0), 0);
  const minutesWin  = (rows || []).reduce((s, r) => s + Number(days <= 30 ? r.minutes_30d : r.minutes_total || 0), 0);
  const avgSession  = sessionsWin ? minutesWin / sessionsWin : 0;

  const actionsWin = useMemo(() => {
    const m = {};
    actions.filter(a => a.event_type === "action" && new Date(a.occurred_at).getTime() > cutoff)
      .forEach(a => { m[a.action] = (m[a.action] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [actions, cutoff]);

  // Empty states, failed loads and blocked pages — these are to-do lists (a data
  // gap / a bug / an access gap), not engagement, so they get their own panel
  // instead of sitting in the action counts.
  //
  // PER USER, not per screen. A screen tally answers "how many" and the only
  // question worth asking here is WHO — the same "🕳 Field Mapper — empty · 4"
  // covered a dairy with no crop ground at all (Laier, correct, and the nav has
  // since been gated by hasCropGround) and a grower holding 97 mapped fields
  // (Howlett). Those are opposite to-dos and the old panel rendered them
  // identically, with no name to call.
  const frictionWin = useMemo(() => {
    const empty = {}, blocked = {}, failed = {};
    const bump = (m, k, a) => {
      const e = m[k] || (m[k] = { screen: a.screen || "?", user_id: a.user_id, n: 0, last: null, reason: null });
      e.n += 1;
      if (!e.last || a.occurred_at > e.last) e.last = a.occurred_at;
      const why = a.detail && a.detail.reason;
      if (why && !e.reason) e.reason = why;
      return e;
    };
    actions.filter(a => a.event_type === "action" && new Date(a.occurred_at).getTime() > cutoff)
      .forEach(a => {
        const s = a.screen || "?";
        if (a.action === "empty_state") bump(empty, `${a.user_id}|${s}`, a);
        if (a.action === "load_error")  bump(failed, `${a.user_id}|${s}`, a);
        if (a.action === "blocked_nav") {
          const k = (a.detail && a.detail.requested) || s;
          bump(blocked, `${a.user_id}|${k}`, { ...a, screen: k });
        }
      });
    const byRecency = (a, b) => String(b.last || "").localeCompare(String(a.last || ""));
    return {
      empty:   Object.values(empty).sort(byRecency),
      failed:  Object.values(failed).sort(byRecency),
      blocked: Object.values(blocked).sort(byRecency),
    };
  }, [actions, cutoff]);

  // ── DO WE ACTUALLY HOLD ANYTHING FOR THEM? ─────────────────────────────────
  // The panel used to ASSERT "our data gap" over every empty page. It is not
  // entitled to: an empty render says the screen had no rows in hand, which is
  // the same picture whether the customer has nothing or we failed to fetch what
  // they have. So ask the table.
  //
  // Counts only — sbCount reads the total out of Content-Range and downloads no
  // rows, so this is one tiny request per (user, screen) that actually dead-ended
  // (two of them today), not a book-wide read on a page that already pulls six
  // rollups. A probe that can't answer returns null and the row says nothing
  // rather than guessing, same rule as everywhere else here.
  const HOLDINGS_PROBE = {
    fields: { table: "policy_fields", by: "farmer", noun: "mapped field" },
    fsa578: { table: "farmer_578s",   by: "farmer", noun: "document" },
    claims: { table: "indemnities",   by: "policy", noun: "claim" },
    alerts: { table: "price_alerts",  by: "user",   noun: "alert" },
  };
  // Grants come off the `users` roster this screen was already handed
  // (/api/portal-invite returns each user's farmer_portal_users rows as
  // `grants`), NOT a second read of the table. A separate fetch is one more
  // thing that can come back empty and be mistaken for "this customer has no
  // farms" — which is the exact false verdict this panel exists to stop
  // printing. A user missing from the roster resolves to UNDEFINED, not [], so
  // the probe below can tell "no grant" from "don't know".
  const grantsByUser = useMemo(() => {
    const m = {};
    (users || []).forEach(u => {
      m[u.user_id] = (u.grants || []).filter(g => g.is_active !== false).map(g => g.farmer_id).filter(Boolean);
    });
    return m;
  }, [users]);
  const [holdings, setHoldings] = useState({});             // `${user}|${screen}` -> count | null
  // Keyed on a STRING of the pairs, never on the array identity — frictionWin
  // mints a new array whenever `actions` or the window changes, and an effect
  // that both depends on an object literal and calls setState is the loop that
  // ate the Field Mapper (see the note on `pricesKey` in FieldDataView).
  const deadEndKey = [...frictionWin.empty, ...frictionWin.failed]
    .map(e => `${e.user_id}|${e.screen}`).sort().join(",");
  useEffect(() => {
    if (!grantsByUser || !deadEndKey) return;
    let alive = true;
    (async () => {
      const pairs = deadEndKey.split(",").filter(Boolean);
      const out = {};
      for (const key of pairs) {
        const [uid, screen] = key.split("|");
        const probe = HOLDINGS_PROBE[screen];
        if (!probe) continue;
        let n = null;
        if (probe.by === "user") {
          n = await sbCount(probe.table, [`user_id=eq.${uid}`]);
        } else {
          const fids = grantsByUser[uid];
          if (fids == null) { out[key] = null; continue; }   // not on the roster — unknown, not zero
          if (!fids.length) { out[key] = 0; continue; }      // on the roster with no farm IS the answer
          const list = `(${fids.join(",")})`;
          n = probe.by === "policy"
            ? await sbCount(probe.table, [`policies.farmer_id=in.${list}`], `id,policies!inner(farmer_id)`)
            : await sbCount(probe.table, [`farmer_id=in.${list}`]);
        }
        out[key] = n;
      }
      if (alive) setHoldings(out);
    })();
    return () => { alive = false; };
  }, [deadEndKey, grantsByUser]);  // eslint-disable-line

  const whoBy = useMemo(() => {
    const m = {};
    (users || []).forEach(u => { m[u.user_id] = u.display_name || u.email || u.user_id; });
    return m;
  }, [users]);

  // Leaderboard: every active invitee, most-engaged first. Users with no logged
  // activity sort to the bottom rather than disappearing — the dormant ones are
  // the whole point of looking at this page.
  const board = useMemo(() => active.map(u => {
    const r = usageById[u.user_id] || {};
    const seen = r.last_seen || lastActive[u.user_id] || u.last_sign_in_at || null;
    return {
      ...u,
      minutes: Number((days <= 30 ? r.minutes_30d : r.minutes_total) || 0),
      sessions: Number((days <= 30 ? r.sessions_30d : r.sessions) || 0),
      activeDays: Number((days <= 30 ? r.active_days_30d : r.active_days) || 0),
      topScreen: r.top_screen || null,
      lastSeen: seen,
      // How we know: in-app events > auth session refresh > password login.
      seenVia: r.last_seen ? "activity" : (lastActive[u.user_id] ? "open" : (u.last_sign_in_at ? "login" : null)),
    };
  }).sort((a, b) => (b.minutes - a.minutes) || (b.sessions - a.sessions)
    || (new Date(b.lastSeen || 0) - new Date(a.lastSeen || 0))), [active, usageById, lastActive, days]);

  const nudge = board.filter(u => !u.lastSeen || new Date(u.lastSeen).getTime() < Date.now() - 30 * 86400000);
  const maxScreen = Math.max(1, ...screens.map(s => Number(days <= 30 ? s.views_30d : s.views) || 0));
  const maxDaily  = Math.max(1, ...daily.slice(0, 60).map(d => Number(d.sessions || 0)));
  const logStart  = (rows || []).reduce((min, r) => {
    const t = new Date(r.first_seen).getTime();
    return (!min || t < min) ? t : min;
  }, null);

  // ── Retention: does a first visit turn into a second one? ──────────────────
  // The strongest single predictor of whether a portal user sticks. Measured
  // over users whose FIRST session is old enough to have had a fair chance to
  // return (7 days), so today's new signups don't drag the rate down.
  const retention = useMemo(() => {
    const byUser = {};
    sessions.forEach(s => { (byUser[s.user_id] = byUser[s.user_id] || []).push(new Date(s.started_at).getTime()); });
    const eligible = [], returned = [], gaps = [];
    Object.values(byUser).forEach(list => {
      list.sort((a, b) => a - b);
      if (Date.now() - list[0] < 7 * 86400000) return;   // too new to judge
      eligible.push(list);
      if (list.length > 1) { returned.push(list); gaps.push((list[1] - list[0]) / 86400000); }
    });
    gaps.sort((a, b) => a - b);
    return {
      eligible: eligible.length,
      returned: returned.length,
      rate: eligible.length ? returned.length / eligible.length : null,
      medianGapDays: gaps.length ? gaps[Math.floor(gaps.length / 2)] : null,
    };
  }, [sessions]);

  // ── Depth: did they look around, or check one number and leave? ───────────
  const depth = useMemo(() => {
    const win2 = sessions.filter(s => new Date(s.started_at).getTime() > cutoff);
    const b = { glance: 0, brief: 0, explored: 0 };
    win2.forEach(s => {
      const n = Number(s.screens_touched || 0);
      if (n <= 1) b.glance++; else if (n <= 3) b.brief++; else b.explored++;
    });
    return { ...b, total: win2.length };
  }, [sessions, cutoff]);

  // ── Attribution: did the emails actually bring anyone in? ─────────────────
  // Sessions that arrived via an ?src=-tagged link, vs the untagged baseline.
  const srcWin = useMemo(() => {
    const m = {};
    let direct = 0;
    actions.filter(a => a.event_type === "session_start" && new Date(a.occurred_at).getTime() > cutoff)
      .forEach(a => {
        const s = a.detail && a.detail.src;
        if (s) m[s] = (m[s] || 0) + 1; else direct++;
      });
    return { tagged: Object.entries(m).sort((a, b) => b[1] - a[1]), direct };
  }, [actions, cutoff]);

  const maxHour = Math.max(1, ...hours.map(h => Number(h.sessions || 0)));

  // ── Chart tokens ────────────────────────────────────────────────────────────
  // The ordinal ramp and the text tokens below were validated, not eyeballed
  // (dataviz validate_palette.js): the ramp passes monotone-lightness, adjacent
  // ΔL ≥ 0.06, single-hue and light-end contrast on white; UT.mute (#657565) and
  // UT.warn (#8a5e10) clear 4.5:1 for small text, which the app's usual #677167
  // (2.49:1) and #b07a20 (3.72:1) do not at 10–12px.
  const rel = (ts) => {
    if (!ts) return "never";
    const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d}d ago`;
  };
  const card = { background:"#fff", border:"1px solid #e0e6e0", borderRadius:12, padding:"14px 16px" };
  // Proportional figures on the big numbers — tabular-nums makes a standalone
  // "121" look loose at display sizes. It's kept below on the table rows, where
  // digits actually need to line up.
  const kpiN = { fontSize:isMobile?21:26, fontWeight:800, color:UT.ink, lineHeight:1.05 };
  const kpiL = { fontSize:10, fontWeight:700, color:UT.mute, textTransform:"uppercase", letterSpacing:"0.07em", marginTop:5 };
  const th   = { textAlign:"left", fontSize:10, fontWeight:700, color:UT.mute, textTransform:"uppercase", letterSpacing:"0.06em", padding:"7px 8px", borderBottom:"1px solid #e0e6e0", whiteSpace:"nowrap" };
  const td   = { fontSize:12.5, color:UT.body, padding:"8px 8px", borderBottom:"1px solid #f0f4f0" };

  if (loading) return <div style={{ textAlign:"center", padding:40, color:UT.mute }}>Loading usage…</div>;
  if (err) return <div style={{ background:"#fdecea", color:"#c0392b", padding:"10px 14px", borderRadius:8, fontSize:13 }}>{err}</div>;

  // Daily series, gap-filled so a quiet day is a zero rather than a missing point.
  const series = usageFillDays(daily, 60);
  const winSeries = series.slice(-days);
  const prevSeries = series.slice(-days * 2, -days);
  const prevSessions = prevSeries.reduce((s, d) => s + d.sessions, 0);
  const prevViews    = prevSeries.reduce((s, d) => s + d.screen_views, 0);
  const collecting = !logStart || sessionsWin === 0;

  // Funnel stages are ORDINAL (the order is the meaning), so they take one hue in
  // monotone steps rather than eight identities.
  const stages = [
    { l:"Invited",             n: active.length, note: users.length - active.length ? `${users.length - active.length} revoked` : "all active" },
    { l:"Ever opened it",      n: everIn.length, note: "signed in at least once" },
    { l:`Active · ${days}d`,   n: recent.length, note: "opened it in the window" },
    ...(days === 7 ? [] : [{ l:"Active · 7d", n: weekly.length, note: "back this week" }]),
  ];
  const funnelMax = Math.max(1, ...stages.map(s => s.n));

  // The heatmap's numbers must be readable without hovering, so name the top
  // windows in text. A 168-cell table view would be worse than useless here;
  // the three busiest slots are the whole actionable content anyway.
  const DOW_LBL = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const hourLbl = (h) => h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h-12}pm`;
  const busiest = [...hours]
    .sort((a, b) => Number(b.sessions) - Number(a.sessions))
    .slice(0, 3)
    .filter(h => Number(h.sessions) > 0)
    .map(h => `${DOW_LBL[Number(h.dow)]} ${hourLbl(Number(h.hour))} (${h.sessions})`);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:12 }}>
        <p style={{ color:UT.body, fontSize:13, margin:0 }}>
          Who opens the portal, how often, and what they use once they're in.
        </p>
        <div style={{ display:"inline-flex", background:"#eef3ec", borderRadius:8, padding:3, gap:2 }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={()=>setDays(d)}
              style={{ padding:"5px 14px", borderRadius:6, fontSize:12, fontWeight:800, cursor:"pointer", border:"none",
                       background: days===d ? UT.ramp[3] : "transparent", color: days===d ? "#fff" : UT.body,
                       boxShadow: days===d ? "0 1px 2px rgba(0,0,0,0.14)" : "none" }}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div style={{ background:"#f4f8f2", borderLeft:`3px solid ${UT.ramp[2]}`, color:UT.body, borderRadius:"4px 8px 8px 4px", padding:"9px 13px", fontSize:12, marginBottom:16, lineHeight:1.55 }}>
        <b style={{ color:UT.ink }}>“Last seen” is when someone last opened the app</b>, not when they last typed a password.
        A signed-in user's session refreshes silently, so <code style={{ background:"#e6efe4", padding:"1px 4px", borderRadius:3 }}>last_sign_in_at</code> can read months stale for a daily user — it's used only as a last resort here.
        {logStart && (
          <> Screen and minute detail begins <b>{new Date(logStart).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</b>; recency before that comes from session history.</>
        )}
      </div>

      {/* ── Adoption funnel + the two standing facts ─────────────────────────── */}
      <div style={{ display:"flex", gap:14, flexWrap:"wrap", marginBottom:14 }}>
        <div style={{ ...card, flex:"3 1 420px" }}>
          <div style={{ fontWeight:800, fontSize:13.5, color:UT.ink }}>Adoption</div>
          <div style={{ fontSize:11.5, color:UT.mute, marginBottom:12 }}>Every invited account, and how far down the path it got.</div>
          {stages.map((s, i) => {
            const prev = i ? stages[i-1].n : null;
            const pct  = prev ? Math.round(s.n / Math.max(prev, 1) * 100) : null;
            return (
              <div key={s.l} style={{ marginBottom: i === stages.length-1 ? 0 : 11 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8, marginBottom:3 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:UT.body }}>{s.l}</span>
                  <span style={{ fontSize:11, color:UT.mute }}>
                    {s.note}{pct != null ? ` · ${pct}% of previous` : ""}
                  </span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                  <div style={{ flex:1, height:22, background:"#f2f6f2", borderRadius:5, overflow:"hidden" }}>
                    <div title={`${s.l}: ${s.n} of ${active.length} invited`}
                      style={{ width:`${Math.max(2, s.n / funnelMax * 100)}%`, height:"100%",
                               background:UT.ramp[Math.min(i, UT.ramp.length-1)], borderRadius:"5px 4px 4px 5px" }} />
                  </div>
                  <span style={{ fontSize:16, fontWeight:800, color:UT.ink, minWidth:32, textAlign:"right" }}>{s.n}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ flex:"1 1 190px", display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ ...card, flex:1 }}>
            <div style={kpiN}>{installed.length}<span style={{ fontSize:14, color:UT.mute, fontWeight:700 }}> / {active.length}</span></div>
            <div style={kpiL}>📱 On a phone</div>
            <UsageMeter value={installed.length} total={active.length} label="saved to home screen" />
          </div>
          <div style={{ ...card, flex:1 }}>
            <div style={kpiN}>{retention.rate == null ? "—" : `${Math.round(retention.rate * 100)}%`}</div>
            <div style={kpiL}>↩ Came back</div>
            {retention.eligible > 0
              ? <UsageMeter value={retention.returned} total={retention.eligible}
                  label={`${retention.returned} of ${retention.eligible} returned${retention.medianGapDays != null ? ` · ~${retention.medianGapDays.toFixed(0)}d later` : ""}`} />
              : <div style={{ fontSize:11, color:UT.mute, marginTop:7, lineHeight:1.45 }}>Needs users whose first visit was 7+ days ago.</div>}
          </div>
        </div>
      </div>

      {/* ── Engagement KPI row ───────────────────────────────────────────────── */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:14 }}>
        {[
          { n: sessionsWin, l: `Sessions · ${days}d`, prev: prevSessions, spark: winSeries.map(d => d.sessions) },
          { n: Math.round(minutesWin), l: `Minutes · ${days}d` },
          { n: avgSession ? avgSession.toFixed(1) : "—", l: "Avg session (min)" },
          { n: viewsWin, l: `Page views · ${days}d`, prev: prevViews, spark: winSeries.map(d => d.screen_views) },
          { n: actionsWin.reduce((s,[,c])=>s+c,0), l: `Actions · ${days}d` },
          { n: retention.rate == null ? "—" : `${Math.round(retention.rate * 100)}%`, l: "Came back", t: `${retention.returned} of ${retention.eligible} users whose first visit was 7+ days ago returned for a second session${retention.medianGapDays != null ? ` · median gap ${retention.medianGapDays.toFixed(0)}d` : ""}` },
        ].map(k => (
          <div key={k.l} style={{ ...card, flex:"1 1 150px", minWidth:130, padding:"13px 15px" }}>
            <div style={kpiN}>{typeof k.n === "number" ? k.n.toLocaleString() : k.n}</div>
            <div style={kpiL}>{k.l}</div>
            {k.spark && k.spark.some(v => v > 0) && (
              <div style={{ marginTop:8 }}><UsageSparkline values={k.spark} /></div>
            )}
            {k.prev != null && (k.n > 0 || k.prev > 0) && <UsageDelta now={k.n} prev={k.prev} days={days} />}
          </div>
        ))}
      </div>

      {/* ── Daily sessions ───────────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom:14 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:8 }}>
          <div>
            <div style={{ fontWeight:800, fontSize:13.5, color:UT.ink }}>Daily sessions</div>
            <div style={{ fontSize:11.5, color:UT.mute }}>Last 60 days · a bar per day</div>
          </div>
          <div style={{ fontSize:11, color:UT.mute }}>
            peak {Math.max(0, ...series.map(d => d.sessions))} · {series.filter(d => d.sessions > 0).length} active days
          </div>
        </div>
        {collecting
          ? <UsageEmpty icon="📈" title="Nothing logged yet" note="Session logging started today. The first shape shows up after a few days of real use." />
          : <UsageArea data={series} />}
      </div>

      {/* ── When they open it ────────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom:14, overflowX:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:8, marginBottom:10 }}>
          <div>
            <div style={{ fontWeight:800, fontSize:13.5, color:UT.ink }}>When they open it</div>
            <div style={{ fontSize:11.5, color:UT.mute }}>Eastern time · this is the window to send digests and alerts into</div>
            {busiest.length > 0 && (
              <div style={{ fontSize:11.5, color:UT.body, marginTop:4 }}>
                Busiest: <b style={{ color:UT.ink }}>{busiest.join(" · ")}</b>
              </div>
            )}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:10.5, color:UT.mute }}>
            <span>less</span>
            {UT.ramp.map((c,i) => <span key={i} style={{ width:13, height:13, borderRadius:3, background:c, display:"inline-block" }} />)}
            <span>more</span>
          </div>
        </div>
        {hours.length === 0
          ? <UsageEmpty icon="🕗" title="No timing data yet" note="Needs a few days of sessions before a pattern is readable." />
          : (
          <div style={{ minWidth:600 }}>
            {DOW_LBL.map((dayLbl, d) => (
              <div key={d} style={{ display:"flex", alignItems:"center", gap:3, marginBottom:3 }}>
                <div style={{ width:32, fontSize:10.5, fontWeight:700, color:UT.mute }}>{dayLbl}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const cell = hours.find(x => Number(x.dow) === d && Number(x.hour) === h);
                  const n = Number(cell?.sessions || 0);
                  // Sequential: bucket into the same validated ramp, not a raw alpha.
                  const step = n === 0 ? null : Math.min(UT.ramp.length - 1, Math.floor(n / maxHour * (UT.ramp.length - 0.001)));
                  return (
                    <div key={h} title={`${dayLbl} ${hourLbl(h)} — ${n} session${n === 1 ? "" : "s"}`}
                      style={{ flex:1, height:17, borderRadius:3, background: step == null ? "#f2f6f2" : UT.ramp[step] }} />
                  );
                })}
              </div>
            ))}
            <div style={{ display:"flex", gap:3, marginTop:4 }}>
              <div style={{ width:32 }} />
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={{ flex:1, textAlign:"center", fontSize:9.5, color:UT.mute }}>{h % 3 === 0 ? (h === 0 ? "12a" : h === 12 ? "12p" : h < 12 ? h : h-12) : ""}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display:"flex", gap:14, flexWrap:"wrap", marginBottom:14 }}>
        {/* Session depth — ordinal buckets, one hue */}
        <div style={{ ...card, flex:"1 1 300px" }}>
          <div style={{ fontWeight:800, fontSize:13.5, color:UT.ink }}>How deep they go</div>
          <div style={{ fontSize:11.5, color:UT.mute, marginBottom:12 }}>Pages per session · {days}d. A book of one-page glances is a different problem than low traffic.</div>
          {depth.total === 0 ? <UsageEmpty icon="📄" title="No sessions in this window" note="" compact /> : (
            <>
              <div style={{ display:"flex", height:26, borderRadius:6, overflow:"hidden", gap:2, marginBottom:12 }}>
                {[["glance",1],["brief",2],["explored",4]].map(([k, step]) => depth[k] > 0 && (
                  <div key={k} title={`${depth[k]} session(s)`}
                    style={{ flex:depth[k], background:UT.ramp[step], display:"flex", alignItems:"center", justifyContent:"center",
                             color: step >= 3 ? "#fff" : UT.ink, fontSize:11, fontWeight:800 }}>
                    {depth[k] / depth.total > 0.12 ? depth[k] : ""}
                  </div>
                ))}
              </div>
              {[
                { k:"glance",   step:1, l:"1 page — checked one thing" },
                { k:"brief",    step:2, l:"2–3 pages — a quick look" },
                { k:"explored", step:4, l:"4+ pages — really used it" },
              ].map(b => (
                <div key={b.k} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, marginBottom:5 }}>
                  <span style={{ width:11, height:11, borderRadius:3, background:UT.ramp[b.step], flexShrink:0 }} />
                  <span style={{ color:UT.body, flex:1 }}>{b.l}</span>
                  <span style={{ color:UT.mute, fontVariantNumeric:"tabular-nums" }}>{depth[b.k]} · {Math.round(depth[b.k] / depth.total * 100)}%</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Where sessions came from */}
        <div style={{ ...card, flex:"1 1 280px" }}>
          <div style={{ fontWeight:800, fontSize:13.5, color:UT.ink }}>Where sessions came from</div>
          <div style={{ fontSize:11.5, color:UT.mute, marginBottom:12 }}>Alert and digest emails link in tagged, so we can tell whether they pull people back.</div>
          {srcWin.tagged.length === 0 && !srcWin.direct ? (
            <UsageEmpty icon="✉️" title="No tagged arrivals yet" note="Tags land the next time a digest or alert email goes out." compact />
          ) : (
            <div>
              {srcWin.tagged.map(([s, c], i) => (
                <UsageRow key={s} label={{ alert_lrp:"🔔 LRP price alert", alert_drp:"🔔 DRP price alert",
                                           digest_lrp:"📬 Daily LRP digest", digest_drp:"📬 Daily DRP digest" }[s] || s}
                  value={c} max={Math.max(srcWin.direct, ...srcWin.tagged.map(t=>t[1]))} color={UT.ramp[3]} />
              ))}
              {/* Emphasis, not an ordinal step: the email-driven rows are the
                  question, so the untagged baseline goes recessive gray rather
                  than a lighter green (which would read as "less" on a ramp). */}
              <UsageRow label="Direct / bookmark / home screen" value={srcWin.direct}
                max={Math.max(srcWin.direct, ...srcWin.tagged.map(t=>t[1]), 1)} color="#c5cfc5" />
            </div>
          )}
        </div>

        {/* Dead ends — status tone, icon + label (never color alone).
            NAMES THE CUSTOMER, and says whether we hold their data. The screen
            tally this replaced ("Field Mapper — empty · 4") could not tell a
            dairy with no crop ground from a grower with 97 mapped fields whose
            page failed to draw them, and called both "our data gap". */}
        <div style={{ ...card, flex:"1 1 340px", borderColor:"#e6dcbc", background:"#fffdf7" }}>
          <div style={{ fontWeight:800, fontSize:13.5, color:UT.warn }}>⚠ Dead ends</div>
          <div style={{ fontSize:11.5, color:UT.mute, marginBottom:12 }}>
            Who opened a page and got nothing · {days}d. The verdict on each row is the row count we
            actually hold for that customer — a page that failed to load is a bug, not a data gap.
          </div>
          {frictionWin.empty.length === 0 && frictionWin.failed.length === 0 && frictionWin.blocked.length === 0 ? (
            <div style={{ fontSize:12.5, color:UT.ramp[3], fontWeight:600 }}>✓ No dead ends logged.</div>
          ) : (
            <div>
              {[
                ...frictionWin.failed.map(e => ({ ...e, kind:"failed" })),
                ...frictionWin.empty.map(e => ({ ...e, kind:"empty" })),
                ...frictionWin.blocked.map(e => ({ ...e, kind:"blocked" })),
              ].map((e, i) => {
                const held = holdings[`${e.user_id}|${e.screen}`];
                const probe = HOLDINGS_PROBE[e.screen];
                // Three tones, each with its own icon and word — never colour alone.
                const tone = e.kind === "failed" ? UT.bad : e.kind === "blocked" ? UT.body : UT.warn;
                const icon = e.kind === "failed" ? "🐞" : e.kind === "blocked" ? "🔒" : "🕳";
                const verb = e.kind === "failed" ? "failed to load" : e.kind === "blocked" ? "no access" : "empty";
                let verdict;
                if (e.kind === "blocked") verdict = "they expected this page — grant it or explain it";
                else if (e.reason) verdict = e.reason;                       // the load error says it itself
                else if (!probe || held == null) verdict = null;             // nothing to check it against
                else if (held > 0) verdict = `we hold ${held.toLocaleString()} ${probe.noun}${held === 1 ? "" : "s"} for them — OUR BUG, not their data`;
                else verdict = `nothing on file for them yet — a real gap`;
                return (
                  <div key={`${e.kind}-${e.user_id}-${e.screen}-${i}`}
                    style={{ display:"flex", alignItems:"baseline", gap:8, padding:"7px 0",
                             borderTop: i ? "1px solid #f0ead8" : "none" }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12.5, fontWeight:700, color:UT.ink }}>
                        {icon} {PORTAL_SCREEN_LABELS[e.screen] || e.screen}
                        <span style={{ color:tone, fontWeight:700 }}> — {verb}</span>
                      </div>
                      <div style={{ fontSize:11, color:UT.mute, marginTop:2 }}>
                        {whoBy[e.user_id] || "unknown user"}
                        {e.last ? ` · ${new Date(e.last).toLocaleDateString("en-US", { month:"short", day:"numeric" })}` : ""}
                      </div>
                      {verdict && (
                        <div style={{ fontSize:11, color: held > 0 && e.kind === "empty" ? UT.bad : UT.mute, marginTop:2 }}>
                          {verdict}
                        </div>
                      )}
                    </div>
                    <div style={{ fontWeight:800, fontSize:12.5, color:UT.ink, fontVariantNumeric:"tabular-nums" }}
                         title={`logged ${e.n} time${e.n === 1 ? "" : "s"} in this window`}>{e.n}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ display:"flex", gap:14, flexWrap:"wrap", marginBottom:14 }}>
        {/* Most-used pages — nominal categories, so ONE hue; length carries the value */}
        <div style={{ ...card, flex:"2 1 380px" }}>
          <div style={{ fontWeight:800, fontSize:13.5, color:UT.ink }}>Most-used pages</div>
          <div style={{ fontSize:11.5, color:UT.mute, marginBottom:12 }}>{days <= 30 ? "Last 30 days" : "All time"} · views and how many people</div>
          {screens.length === 0 ? <UsageEmpty icon="🗂" title="No page views logged yet" note="" compact /> : (
            <div>
              {[...screens].sort((a,b)=>Number(days<=30?b.views_30d:b.views)-Number(days<=30?a.views_30d:a.views)).map((s, i) => {
                const v = Number(days <= 30 ? s.views_30d : s.views) || 0;
                const u = Number(days <= 30 ? s.users_30d : s.users) || 0;
                return (
                  <UsageRow key={s.screen} rank={i+1} label={PORTAL_SCREEN_LABELS[s.screen] || s.screen}
                    value={v} suffix={`${u} ${u === 1 ? "user" : "users"}`} max={maxScreen} color={UT.ramp[3]} />
                );
              })}
            </div>
          )}
        </div>

        {/* What they did */}
        <div style={{ ...card, flex:"1 1 260px" }}>
          <div style={{ fontWeight:800, fontSize:13.5, color:UT.ink }}>What they did</div>
          <div style={{ fontSize:11.5, color:UT.mute, marginBottom:12 }}>Real actions, not just page loads · {days}d</div>
          {actionsWin.filter(([a]) => !FRICTION_ACTIONS.has(a)).length === 0
            ? <UsageEmpty icon="🖱" title="No actions logged yet" note="" compact />
            : (
              <div>
                {actionsWin.filter(([a]) => !FRICTION_ACTIONS.has(a)).map(([a, c]) => (
                  <UsageRow key={a} label={PORTAL_ACTION_LABELS[a] || a} value={c}
                    max={Math.max(...actionsWin.map(x=>x[1]), 1)} color={UT.ramp[2]} />
                ))}
              </div>
            )}
        </div>
      </div>

      {/* ── Leaderboard ──────────────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom:14, overflowX:"auto" }}>
        <div style={{ fontWeight:800, fontSize:13.5, color:UT.ink }}>Users by engagement</div>
        <div style={{ fontSize:11.5, color:UT.mute, marginBottom:10 }}>Last {days} days · everyone with access, most engaged first</div>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:620 }}>
          <thead><tr>
            <th style={th}>User</th><th style={th}>Last seen</th><th style={{...th, textAlign:"right"}}>Sessions</th>
            <th style={th}>Minutes</th><th style={{...th, textAlign:"right"}}>Days</th>
            <th style={th}>Top page</th><th style={{...th, textAlign:"center"}}>📱</th>
          </tr></thead>
          <tbody>
            {board.map((u, i) => {
              const maxMin = Math.max(1, ...board.map(b => b.minutes));
              const stale = !u.lastSeen || new Date(u.lastSeen).getTime() < Date.now() - 30 * 86400000;
              return (
                <tr key={u.user_id} style={{ background: i % 2 ? "#fafcf9" : "#fff" }}>
                  <td style={{ ...td, fontWeight:700, color:UT.ink }}>
                    {u.display_name || u.email}
                    <div style={{ fontSize:10.5, color:UT.mute, fontWeight:500 }}>{u.role}</div>
                  </td>
                  <td style={{ ...td, color: stale ? UT.warn : UT.body, fontWeight: stale ? 700 : 500 }}
                      title={u.seenVia === "activity" ? "From in-app activity" : u.seenVia === "open" ? "From an app open (session refresh)" : u.seenVia === "login" ? "From a password sign-in only — they may well have opened it since" : ""}>
                    {u.lastSeen ? rel(u.lastSeen) : "⚠ never opened it"}
                    {u.lastSeen && u.seenVia === "login" && <span style={{ color:UT.mute, fontSize:10.5, fontWeight:500 }}> · login only</span>}
                  </td>
                  <td style={{ ...td, textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{u.sessions || "—"}</td>
                  <td style={td}>
                    {u.minutes ? (
                      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <div style={{ flex:1, minWidth:40, height:7, background:"#f2f6f2", borderRadius:4, overflow:"hidden" }}>
                          <div style={{ width:`${Math.max(3, u.minutes / maxMin * 100)}%`, height:"100%", background:UT.ramp[3] }} />
                        </div>
                        <span style={{ fontWeight:800, minWidth:26, textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{Math.round(u.minutes)}</span>
                      </div>
                    ) : "—"}
                  </td>
                  <td style={{ ...td, textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{u.activeDays || "—"}</td>
                  <td style={td}>{u.topScreen ? (PORTAL_SCREEN_LABELS[u.topScreen] || u.topScreen) : "—"}</td>
                  <td style={{ ...td, textAlign:"center" }}>{u.installed_at ? "📱" : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Who to nudge ─────────────────────────────────────────────────────── */}
      <div style={{ ...card, borderColor:"#e6dcbc", background:"#fffdf7" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontWeight:800, fontSize:13.5, color:UT.warn }}>⚠ Worth a nudge</span>
          <span style={{ background:UT.warn, color:"#fff", borderRadius:20, padding:"1px 9px", fontSize:11, fontWeight:800 }}>{nudge.length}</span>
        </div>
        <div style={{ fontSize:11.5, color:UT.mute, margin:"4px 0 11px" }}>Invited but never opened it, or no app open in the last 30 days.</div>
        {nudge.length === 0 ? <div style={{ fontSize:12.5, color:UT.ramp[3], fontWeight:600 }}>✓ Everyone's been in recently.</div> : (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {nudge.map(u => (
              <div key={u.user_id} style={{ display:"flex", justifyContent:"space-between", gap:10, fontSize:12.5, flexWrap:"wrap",
                                            paddingBottom:5, borderBottom:"1px solid #f2ede0" }}>
                <span style={{ fontWeight:700, color:UT.ink }}>{u.display_name || u.email}</span>
                <span style={{ color:UT.mute }}>
                  {u.lastSeen ? `last opened ${rel(u.lastSeen)}` : "never opened it"} · <a href={`mailto:${u.email}`} style={{ color:UT.ramp[3], fontWeight:600 }}>{u.email}</a>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PortalAdminView({ isMobile = false, onPreview }) {
  const [tab, setTab] = useState("access");   // access | usage
  const [users, setUsers] = useState([]);
  const [farmersList, setFarmersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editUserId, setEditUserId] = useState(null);

  // Roster browsing state. The list is a one-line-per-person roster now — at 36
  // users the old full-detail cards ran several screens deep and you couldn't
  // answer "who has access to what" without scrolling past everything.
  const [usage, setUsage] = useState({});          // user_id → portal_user_usage
  const [lastActive, setLastActive] = useState({});// user_id → real recency
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [sortBy, setSortBy] = useState("recent");
  const [openId, setOpenId] = useState(null);      // expanded row
  const [previewBusy, setPreviewBusy] = useState(null); // user_id being minted

  // Open the portal in the target user's OWN session.
  //
  // `onPreview` (setPreviewAs) is no longer called: beginPreviewSession reloads
  // the page, and Dashboard rehydrates previewAs from sessionStorage on the way
  // back up. The prop is kept as the "preview is available here" gate. The
  // reload is the whole point — it guarantees no admin-fetched rows and no
  // _sbCache entries survive into the impersonated session.
  async function startPreview(u) {
    if (!u || !u.user_id) return;
    setPreviewBusy(u.user_id); setErr(null);
    try {
      const r = await adminApiFetch(`/api/portal-invite?fn=preview`, {
        method: "POST",
        body: JSON.stringify({ user_id: u.user_id }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.access_token) {
        setErr(body.error || `Could not open preview (HTTP ${r.status})`);
        setPreviewBusy(null);
        return;
      }
      beginPreviewSession(body.access_token, {
        grants: u.grants || [],
        role: u.role,
        // Same null-vs-list distinction PortalGate keeps: null = follow coverage.
        marketTabs: Array.isArray(u.market_tabs) ? u.market_tabs : null,
        // Preview must reproduce the page allowlist too, or "preview as" shows a
        // staff member a nav they do not have — the one thing preview exists to
        // rule out.
        pageAccess: Array.isArray(u.page_access) ? u.page_access : null,
        canViewMi: !!u.can_view_mi, canViewElevator: !!u.can_view_elevator, canSetAlerts: !!u.can_set_alerts, canView578: !!u.can_view_578, bookWide578: !!(u.can_view_578 && u.book_wide_578), canViewModelFarm: !!u.can_view_model_farm,
        canViewCashTargets: !!u.can_view_cash_targets,
        // Preview must carry this too, or previewing a limited agent shows a nav she
        // does not have - or hides one she does, which is how this defect surfaced.
        canView426: !!u.can_view_426,
        displayName: u.display_name || u.email,
        email: u.email,
        homeLat: u.home_lat, homeLon: u.home_lon, homeLabel: u.home_label,
        farmerIds: (u.grants || []).map(g => g.farmer_id).filter(Boolean),
      });
    } catch (e) {
      setErr(String(e.message || e));
      setPreviewBusy(null);
    }
  }

  // Invite/edit form state
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("farmer");
  const [canViewMi, setCanViewMi] = useState(false);
  const [canViewElevator, setCanViewElevator] = useState(false);
  const [canSetAlerts, setCanSetAlerts] = useState(false);
  const [canView578, setCanView578] = useState(false);
  const [canViewModelFarm, setCanViewModelFarm] = useState(false);
  // null = follow their coverage grants (the default). An array is an explicit
  // per-user allowlist of MarketsHub tab keys, [] meaning no market pages at all.
  const [marketTabs, setMarketTabs] = useState(null);
  // null = follow the ordinary grant/flag gates (the default, and what every
  // user had before sql/049). An array is an explicit page allowlist, [] meaning
  // no pages at all — the same three-valued shape as marketTabs above, for the
  // same reason: collapsing [] into null hands the whole nav back to somebody an
  // admin had just switched every page off for.
  const [pageAccess, setPageAccess] = useState(null);
  const [canViewCashTargets, setCanViewCashTargets] = useState(false);
  const [grants, setGrants] = useState([]);
  // The coverage lines currently ticked on the form, so the editor can show which
  // markets "follow coverage" would actually produce as it is being filled in.
  const grantLobs = useMemo(() => {
    const s = new Set();
    (grants || []).forEach(g => (g.lobs || []).forEach(l => s.add(l)));
    // Markets widen past coverage for DRP/LRP — mirror Dashboard's marketLobSet
    // so the preview line here matches what the farmer will actually get.
    return s;
  }, [grants]);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const [uRes, fRes] = await Promise.all([
        adminApiFetch(`/api/portal-invite`),
        fetch(`${SUPABASE_URL}/rest/v1/farmers?select=id,name&order=name.asc`, { headers: getAuthHeaders() }),
      ]);
      if (!uRes.ok) throw new Error(`Load users failed: ${await uRes.text()}`);
      const uJson = await uRes.json();
      setUsers(uJson.users || []);
      setFarmersList(fRes.ok ? await fRes.json() : []);
    } catch (e) { setErr(String(e.message || e)); }
    setLoading(false);

    // Usage is best-effort and deliberately AFTER setLoading(false): the access
    // list is the job of this screen and must render even if analytics 404 or
    // the events table is empty. A failure here leaves the roster showing "—"
    // for activity, never an error banner.
    try {
      const [uu, laRes] = await Promise.all([
        sbFetch("portal_user_usage").catch(() => []),
        fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_last_active`, {
          method: "POST", headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, body: "{}",
        }).catch(() => null),
      ]);
      const byId = {};
      (uu || []).forEach(r => { byId[r.user_id] = r; });
      setUsage(byId);
      if (laRes && laRes.ok) {
        const la = {};
        (await laRes.json()).forEach(r => { la[r.user_id] = r.last_active_at; });
        setLastActive(la);
      }
    } catch { /* roster still renders */ }
  }
  useEffect(() => { load(); }, []);

  function resetForm() {
    setEmail(""); setDisplayName(""); setRole("farmer"); setCanViewMi(false); setCanViewElevator(false); setCanSetAlerts(false); setCanView578(false); setCanViewModelFarm(false); setGrants([]); setMarketTabs(null);
    setShowInvite(false); setEditUserId(null);
  }

  function startEdit(u) {
    setEditUserId(u.user_id);
    setEmail(u.email || "");
    setDisplayName(u.display_name || "");
    setRole(u.role || "farmer");
    setCanViewMi(!!u.can_view_mi);
    setCanViewElevator(!!u.can_view_elevator);
    setCanSetAlerts(!!u.can_set_alerts);
    setCanView578(!!u.can_view_578);
    setCanViewModelFarm(!!u.can_view_model_farm);
    // Anything that isn't an array means "follow coverage" — keep it null so a
    // plain re-save (changing a display name, say) doesn't quietly freeze their
    // markets into an explicit list that stops tracking their coverage.
    setMarketTabs(Array.isArray(u.market_tabs) ? u.market_tabs : null);
    // Same rule, same reason: a plain re-save must not freeze a "follow the
    // gates" user into an explicit page list.
    setPageAccess(Array.isArray(u.page_access) ? u.page_access : null);
    setCanViewCashTargets(!!u.can_view_cash_targets);
    setGrants((u.grants || []).map(g => ({ farmer_id: g.farmer_id, lobs: (g.permissions && g.permissions.lobs) || [] })));
    setShowInvite(false);
    // The editor renders above the roster. Clicking Edit on someone 30 rows
    // down otherwise looks like nothing happened.
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* older browsers */ }
  }

  async function sendInvite() {
    if (!email.trim()) { alert("Email is required."); return; }
    setBusy(true); setErr(null);
    try {
      const res = await adminApiFetch(`/api/portal-invite`, {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), display_name: displayName.trim() || null, role, can_view_mi: canViewMi, can_view_elevator: canViewElevator, can_set_alerts: canSetAlerts, can_view_578: canView578, can_view_model_farm: canViewModelFarm, market_tabs: marketTabs, page_access: pageAccess, can_view_cash_targets: canViewCashTargets, grants }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || "Invite failed");
      alert(j.reused_existing
        ? "User already existed — a fresh temporary password was emailed and permissions updated."
        : "Invite sent — the user gets an email with a temporary password and a link to sign in.");
      resetForm(); await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  async function saveEdit() {
    setBusy(true); setErr(null);
    try {
      const res = await adminApiFetch(`/api/portal-invite`, {
        method: "PATCH",
        body: JSON.stringify({ user_id: editUserId, display_name: displayName.trim() || null, role, can_view_mi: canViewMi, can_view_elevator: canViewElevator, can_set_alerts: canSetAlerts, can_view_578: canView578, can_view_model_farm: canViewModelFarm, market_tabs: marketTabs, page_access: pageAccess, can_view_cash_targets: canViewCashTargets, grants }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || "Save failed");
      resetForm(); await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  async function setActive(u, active) {
    setBusy(true); setErr(null);
    try {
      const res = await adminApiFetch(`/api/portal-invite`, {
        method: "PATCH",
        body: JSON.stringify({ user_id: u.user_id, is_active: active }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  async function resetPassword(u) {
    if (!window.confirm(`Email ${u.email} a new temporary password? Their current password will stop working.`)) return;
    setBusy(true); setErr(null);
    try {
      const res = await adminApiFetch(`/api/portal-invite`, { method: "PATCH", body: JSON.stringify({ user_id: u.user_id, reset_password: true }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || "Reset failed");
      // When the email can't go out, show the temp password so it can be read
      // out over the phone. Previously this said "no email on file" and the new
      // password was lost — a reset nobody could act on.
      alert(j.emailed
        ? `A new temporary password was emailed to ${j.email || u.email}.`
        : `Password reset — but the email did NOT send${j.email_error ? ` (${j.email_error})` : ""}.\n\n` +
          `Temporary password for ${j.email || u.email}:\n\n    ${j.temp_password}\n\n` +
          `Read it to them, then have them change it after signing in.`);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  // Boot one person off every device without touching their access — the
  // "they left the app open on a shared tractor iPad" case.
  async function signOutUser(u) {
    if (!window.confirm(`Sign ${u.email} out on every device?\n\nTheir access is unchanged — they'll just have to sign in again.`)) return;
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_signout_user`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ p_user_id: u.user_id }),
      });
      if (!res.ok) throw new Error(await res.text());
      const n = await res.json();
      alert(`Signed out ${n} session${n === 1 ? "" : "s"}.`);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  async function hardDelete(u) {
    if (!window.confirm(`Permanently delete ${u.email}? This removes their login and all access. This cannot be undone.`)) return;
    setBusy(true); setErr(null);
    try {
      const res = await adminApiFetch(`/api/portal-invite?user_id=${u.user_id}&hard=1`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }

  const nameFor = (id) => (farmersList.find(f => f.id === id)?.name) || id;
  const btn = (bg) => ({ padding:"8px 16px", background:bg, color:"#fff", border:"none", borderRadius:7, fontWeight:800, fontSize:13, cursor:"pointer" });

  // Real recency, not last_sign_in_at. That column only moves on a fresh
  // password sign-in, so anyone who stays signed in — which is everyone on a
  // phone — reads as inactive for months. portal_last_active() is the truth;
  // last_sign_in_at is the fallback when a user predates event tracking.
  const recencyOf = (u) => lastActive[u.user_id] || u.last_sign_in_at || null;
  const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
  const relLabel = (iso) => {
    const d = daysSince(iso);
    if (d === null) return "never";
    if (d <= 0) return "today";
    if (d === 1) return "yesterday";
    if (d < 30) return `${d}d ago`;
    if (d < 365) return `${Math.floor(d / 30)}mo ago`;
    return `${Math.floor(d / 365)}y ago`;
  };
  // Green recent, amber cooling, grey cold, orange never — same scale the
  // freshness pills use elsewhere so the colours mean one thing app-wide.
  const relColor = (iso) => {
    const d = daysSince(iso);
    if (d === null) return "#b07a20";
    if (d <= 7) return "#2d7a2d";
    if (d <= 30) return "#7a6a20";
    return "#60735f";
  };
  const flagsOf = (u) => [
    u.can_view_mi       && { k:"mi",    icon:"📈", label:"Market Intelligence" },
    u.can_set_alerts    && { k:"al",    icon:"🔔", label:"Price alerts" },
    u.can_view_578      && { k:"f578",  icon:"📋", label: u.book_wide_578 ? "578 files (entire book)" : "578 files (own farms)" },
    u.can_view_elevator && { k:"elev",  icon:"🌾", label:"Elevator bids" },
    // Only when it's an explicit list — "following coverage" is the norm and
    // would put this chip on almost every row, which says nothing.
    Array.isArray(u.market_tabs) && { k:"mkt", icon:"💹",
      label: u.market_tabs.length
        ? `Custom markets: ${u.market_tabs.map(t => (MARKET_TABS.find(m => m.key === t) || {}).label || t).join(", ")}`
        : "Custom markets: none (Markets page hidden)" },
  ].filter(Boolean);

  // An admin buckets as "admin" regardless of any portal_users.role they also
  // carry — Mike holds role='farmer' on his portal row and is an agency admin,
  // and filtering "Farmers" should not surface him as a customer.
  const bucketOf = (u) => (u.is_admin ? "admin" : (u.role || "farmer"));

  const roster = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = users.filter(u => {
      if (statusFilter === "active"    && !u.is_active) return false;
      if (statusFilter === "revoked"   &&  u.is_active) return false;
      if (statusFilter === "never"     &&  recencyOf(u)) return false;
      if (statusFilter === "onphone"   && !u.installed_at) return false;
      if (statusFilter === "nofarms"   && ((u.grants || []).filter(g => g.is_active !== false).length > 0 || u.is_admin)) return false;
      if (roleFilter !== "all" && bucketOf(u) !== roleFilter) return false;
      if (!needle) return true;
      // Search farm names too — "who can see Kirby?" is the question this
      // screen gets asked most, and it was unanswerable without scrolling.
      const farms = (u.grants || []).map(g => nameFor(g.farmer_id)).join(" ");
      return `${u.display_name || ""} ${u.email || ""} ${farms}`.toLowerCase().includes(needle);
    });
    const nameKey = (u) => (u.display_name || u.email || "").toLowerCase();
    const key = {
      recent:  (u) => -(new Date(recencyOf(u) || 0).getTime()),
      stale:   (u) => new Date(recencyOf(u) || 0).getTime(),   // coldest first
      name:    nameKey,
      namedesc:(u) => nameKey(u),
      farms:   (u) => -((u.grants || []).filter(g => g.is_active !== false).length),
      role:    (u) => `${bucketOf(u)} ${nameKey(u)}`,
      newest:  (u) => -(new Date(u.created_at || 0).getTime()),
    }[sortBy] || nameKey;
    list = list.sort((a, b) => { const ka = key(a), kb = key(b); return ka < kb ? -1 : ka > kb ? 1 : 0; });
    if (sortBy === "namedesc") list.reverse();
    return list;
  }, [users, q, roleFilter, statusFilter, sortBy, lastActive, farmersList]);

  // Tiles count PORTAL users only. Agency admin logins are listed on this screen
  // so their passwords can be reset, but counting them would quietly inflate
  // "Active users" — they're not customers on the portal.
  const stats = useMemo(() => {
    const portal = users.filter(u => !u.is_admin);
    return {
      active:  portal.filter(u => u.is_active).length,
      recent:  portal.filter(u => u.is_active && daysSince(recencyOf(u)) !== null && daysSince(recencyOf(u)) <= 30).length,
      never:   portal.filter(u => u.is_active && !recencyOf(u)).length,
      onPhone: portal.filter(u => u.is_active && u.installed_at).length,
    };
  }, [users, lastActive]);

  return (
    <div style={{ maxWidth: tab === "usage" ? 1180 : 900 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6, flexWrap:"wrap", gap:10 }}>
        <h1 style={{ ...uiPageTitle(isMobile) }}>Portal Access</h1>
        {tab === "access" && !showInvite && !editUserId && (
          <button onClick={()=>{ resetForm(); setShowInvite(true); }} style={btn("#2d6a2d")}>+ Invite user</button>
        )}
      </div>

      <div style={{ display:"flex", gap:6, margin:"10px 0 16px", borderBottom:"1px solid #e0e6e0" }}>
        {[{ k:"access", l:"👥 Access" }, { k:"usage", l:"📊 Usage" }].map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)}
            style={{ padding:"8px 16px", border:"none", background:"none", cursor:"pointer", fontSize:13, fontWeight:800,
                     color: tab===t.k ? "#1a2e1a" : "#60735f",
                     borderBottom: "2px solid " + (tab===t.k ? "#2d6a2d" : "transparent"), marginBottom:-1 }}>
            {t.l}
          </button>
        ))}
      </div>

      {err && <div style={{ background:"#fdecea", color:"#c0392b", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:14 }}>{err}</div>}

      {tab === "usage" ? (
        loading
          ? <div style={{ textAlign:"center", padding:40, color:"#60735f" }}>Loading…</div>
          : <PortalUsageView users={users} isMobile={isMobile} />
      ) : (
      <>
      <p style={{ color:"#5a6a5a", fontSize:13, marginTop:0, marginBottom:18 }}>
        Invite customers (and limited agents) to a read-only portal. Each user sees only the farms and lines of business you grant. Market-Intelligence access is a separate toggle.
      </p>

      <PortalSystemPanel isMobile={isMobile} />

      {(showInvite || editUserId) && (
        <div style={{ border:"1px solid #c8d8c8", borderRadius:10, padding:isMobile?"14px":"18px 20px", marginBottom:20, background:"#fff" }}>
          <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a", marginBottom:14 }}>
            {editUserId ? `Edit access — ${email}` : "Invite a new user"}
          </div>
          {!editUserId && (
            <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:14 }}>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="email@example.com"
                style={{ flex:"1 1 240px", padding:"8px 10px", borderRadius:6, border:"1px solid #c8d8c8", fontSize:13 }} />
              <input value={displayName} onChange={e=>setDisplayName(e.target.value)} placeholder="Display name (optional)"
                style={{ flex:"1 1 200px", padding:"8px 10px", borderRadius:6, border:"1px solid #c8d8c8", fontSize:13 }} />
            </div>
          )}
          {editUserId && (
            <input value={displayName} onChange={e=>setDisplayName(e.target.value)} placeholder="Display name (optional)"
              style={{ width:"100%", maxWidth:300, padding:"8px 10px", borderRadius:6, border:"1px solid #c8d8c8", fontSize:13, marginBottom:14, boxSizing:"border-box" }} />
          )}
          <PortalPermissionEditor farmersList={farmersList} grants={grants} setGrants={setGrants}
            canViewMi={canViewMi} setCanViewMi={setCanViewMi} canViewElevator={canViewElevator} setCanViewElevator={setCanViewElevator} canSetAlerts={canSetAlerts} setCanSetAlerts={setCanSetAlerts}
            canView578={canView578} setCanView578={setCanView578}
            canViewModelFarm={canViewModelFarm} setCanViewModelFarm={setCanViewModelFarm}
            marketTabs={marketTabs} setMarketTabs={setMarketTabs}
            pageAccess={pageAccess} setPageAccess={setPageAccess}
            canViewCashTargets={canViewCashTargets} setCanViewCashTargets={setCanViewCashTargets}
            grantLobs={grantLobs}
            role={role} setRole={setRole} isMobile={isMobile} />
          <div style={{ display:"flex", gap:10, marginTop:18 }}>
            <button disabled={busy} onClick={editUserId ? saveEdit : sendInvite} style={{ ...btn("#2d6a2d"), opacity:busy?0.6:1 }}>
              {busy ? "Saving…" : editUserId ? "Save changes" : "Send invite"}
            </button>
            <button disabled={busy} onClick={resetForm} style={btn("#60735f")}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:"#60735f" }}>Loading…</div>
      ) : users.length === 0 ? (
        <div style={{ textAlign:"center", padding:40, color:"#677167", fontSize:14 }}>No portal users yet. Click “Invite user” to add the first one.</div>
      ) : (
      <>
        {/* At-a-glance roster health. "Active in 30d" is the number that says
            whether the portal is actually being used; the other three are the
            follow-up lists. Each tile filters the roster below. */}
        <div style={{ display:"grid", gridTemplateColumns:`repeat(${isMobile?2:4}, 1fr)`, gap:8, marginBottom:12 }}>
          {[
            { k:"active",  n:stats.active,  l:"Active users", c:"#1a2e1a", f:{ status:"active", role:"all" } },
            { k:"recent",  n:stats.recent,  l:"Used in 30d",  c:"#2d7a2d", f:null },
            { k:"never",   n:stats.never,   l:"Never signed in", c: stats.never ? "#b07a20" : "#60735f", f:{ status:"never", role:"all" } },
            { k:"onPhone", n:stats.onPhone, l:"On phone",     c:"#2d6a2d", f:null },
          ].map(t => (
            <button key={t.k} onClick={()=>{ if (t.f) { setStatusFilter(t.f.status); setRoleFilter(t.f.role); setQ(""); } }}
              style={{ textAlign:"left", background:"#fff", border:"1px solid #e0e6e0", borderRadius:9, padding:"10px 12px",
                       cursor: t.f ? "pointer" : "default" }}>
              <div style={{ fontSize:22, fontWeight:800, color:t.c, lineHeight:1.1 }}>{t.n}</div>
              <div style={{ fontSize:11, color:"#60735f", fontWeight:700, marginTop:2 }}>{t.l}</div>
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:10 }}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name, email or farm…"
            style={{ flex:"1 1 220px", padding:"8px 10px", borderRadius:7, border:"1px solid #c8d8c8", fontSize:13 }} />
          {[["all","All"],["farmer","Farmers"],["agent","Agents"],["underwriter","Underwriter"],["admin","Admins"]].map(([k,l]) => {
            const n = k === "all" ? users.length : users.filter(u => bucketOf(u) === k).length;
            if (n === 0 && k !== "all") return null;
            return (
              <button key={k} onClick={()=>setRoleFilter(k)}
                style={{ padding:"7px 12px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer",
                         border:"1px solid " + (roleFilter===k?"#2d6a2d":"#d8e0d8"),
                         background: roleFilter===k?"#e6f0e6":"#fff", color: roleFilter===k?"#2d6a2d":"#6a7a6a" }}>
                {l} <span style={{ opacity:.6, fontWeight:600 }}>{n}</span>
              </button>
            );
          })}
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
            style={{ padding:"7px 9px", borderRadius:7, border:"1px solid #c8d8c8", fontSize:12, fontWeight:700, color:"#5a6a5a" }}>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
            <option value="never">Never signed in</option>
            <option value="onphone">Installed on phone</option>
            <option value="nofarms">No farms granted</option>
            <option value="all">All statuses</option>
          </select>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{ padding:"7px 9px", borderRadius:7, border:"1px solid #c8d8c8", fontSize:12, fontWeight:700, color:"#5a6a5a" }}>
            <option value="recent">Recently active</option>
            <option value="stale">Least recently active</option>
            <option value="name">Name A–Z</option>
            <option value="namedesc">Name Z–A</option>
            <option value="role">Role, then name</option>
            <option value="farms">Most farms</option>
            <option value="newest">Newest added</option>
          </select>
        </div>

        <div style={{ fontSize:11.5, color:"#60735f", marginBottom:8 }}>
          {roster.length} of {users.length} {roster.length === 1 ? "person" : "people"}
          {stats.never > 0 && statusFilter !== "never" &&
            <> · <button onClick={()=>setStatusFilter("never")} style={{ background:"none", border:"none", padding:0, color:"#b07a20", fontWeight:700, fontSize:11.5, cursor:"pointer", textDecoration:"underline" }}>
              {stats.never} never signed in
            </button></>}
        </div>

        {roster.length === 0 ? (
          <div style={{ textAlign:"center", padding:30, color:"#677167", fontSize:13 }}>No one matches that filter.</div>
        ) : (
        <div style={{ border:"1px solid #e0e6e0", borderRadius:10, overflow:"hidden", background:"#fff" }}>
          {roster.map((u, i) => {
            const activeGrants = (u.grants || []).filter(g => g.is_active !== false);
            const open = openId === u.user_id;
            const rec  = recencyOf(u);
            const fl   = flagsOf(u);
            const bookWide = !!(u.can_view_578 && u.book_wide_578);
            return (
              <div key={u.user_id} style={{ borderTop: i === 0 ? "none" : "1px solid #eef2ee", background: open ? "#f7faf7" : (u.is_active ? "#fff" : "#fafafa") }}>
                {/* ── one-line summary ── */}
                <div onClick={()=>setOpenId(open ? null : u.user_id)}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:isMobile?"11px 12px":"11px 14px", cursor:"pointer" }}>
                  <span style={{ width:8, height:8, borderRadius:"50%", flexShrink:0,
                                 background: !u.is_active ? "#c0392b" : relColor(rec) }} />
                  <div style={{ flex:"1 1 auto", minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:14, color: u.is_active ? "#1a2e1a" : "#8a8a8a",
                                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                      {u.display_name || u.email}
                      {u.is_admin && <span style={{ marginLeft:7, fontSize:10, background:"#1a4a7a", color:"#fff", borderRadius:10, padding:"1px 7px", fontWeight:800, verticalAlign:"middle" }}>ADMIN</span>}
                      {!u.is_active && <span style={{ marginLeft:7, fontSize:10.5, color:"#c0392b", fontWeight:800 }}>REVOKED</span>}
                    </div>
                    <div style={{ fontSize:11.5, color:"#60735f", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                      {u.email}
                    </div>
                  </div>
                  {!isMobile && (
                    <div style={{ flex:"0 0 130px", fontSize:12, color:"#5a6a5a", fontWeight:600 }}>
                      {u.is_admin
                        ? <span style={{ color:"#1a4a7a" }}>Agency admin</span>
                        : bookWide
                          ? <span style={{ color:"#7a5a00" }}>Book-wide files</span>
                          : activeGrants.length === 0
                            ? <span style={{ color:"#b07a20" }}>No farms</span>
                            : activeGrants.length === 1
                              ? <span style={{ display:"block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{nameFor(activeGrants[0].farmer_id)}</span>
                              : `${activeGrants.length} farms`}
                    </div>
                  )}
                  {!isMobile && (
                    <div style={{ flex:"0 0 60px", fontSize:13, letterSpacing:1 }} title={fl.map(f=>f.label).join(", ")}>
                      {fl.map(f => f.icon).join("")}
                    </div>
                  )}
                  <div style={{ flex:"0 0 auto", fontSize:12, fontWeight:700, color: relColor(rec), textAlign:"right", minWidth:66 }}>
                    {relLabel(rec)}
                  </div>
                  <span style={{ color:"#a8b8a8", fontSize:12, transform: open ? "rotate(90deg)" : "none", transition:"transform .15s" }}>›</span>
                </div>

                {/* ── drill-down ── */}
                {open && (
                  <div style={{ padding:isMobile?"0 12px 14px":"0 14px 16px" }}>
                    <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:14 }}>

                      {/* What they can see */}
                      <div style={{ border:"1px solid #e0e6e0", borderRadius:9, padding:"11px 13px", background:"#fff" }}>
                        <div style={{ fontSize:11, fontWeight:800, color:"#60735f", textTransform:"uppercase", letterSpacing:.5, marginBottom:8 }}>
                          What they can see
                        </div>
                        <div style={{ fontSize:12, color:"#5a6a5a", marginBottom:8 }}>
                          Role: <b style={{ color:"#2d4a2d" }}>{u.is_admin ? "agency admin" : (u.role || "farmer")}</b>
                        </div>
                        {u.is_admin && (
                          <div style={{ background:"#eef3f8", border:"1px solid #c3d5e8", borderRadius:7, padding:"7px 9px", fontSize:11.5, color:"#1a4a7a", marginBottom:8 }}>
                            🔑 <b>Full agency access</b> — the whole book, every screen, admin tools.
                            Granted by the allowlist in <code>api/_auth.js</code>, not by anything on this page,
                            so it can’t be changed or revoked here. Listed so the password can be reset.
                            {u.no_portal_profile && <> No portal profile — this login goes to the agent app, not the farmer portal.</>}
                          </div>
                        )}
                        {bookWide && (
                          <div style={{ background:"#fff8e6", border:"1px solid #e8d8a8", borderRadius:7, padding:"7px 9px", fontSize:11.5, color:"#7a5a00", marginBottom:8 }}>
                            📋 Book-wide <b>File Management only</b> — every customer's 578s and documents.
                            Every other screen is still limited to the farms below.
                          </div>
                        )}
                        {activeGrants.length === 0 ? (
                          <div style={{ fontSize:12, color:"#9a8060", fontStyle:"italic" }}>No farms granted.</div>
                        ) : (
                          <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:190, overflowY:"auto" }}>
                            {activeGrants.map(g => (
                              <div key={g.farmer_id} style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", fontSize:12 }}>
                                <span style={{ fontWeight:700, color:"#2d4a2d", flex:"1 1 130px" }}>{nameFor(g.farmer_id)}</span>
                                {((g.permissions && g.permissions.lobs) || []).map(l => (
                                  <span key={l} style={{ background:"#e6f0e6", color:"#2d6a2d", borderRadius:12, padding:"1px 8px", fontWeight:700, fontSize:10.5 }}>
                                    {(PORTAL_LOBS.find(x=>x.key===l)||{}).label || l}
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                        {fl.length > 0 && (
                          <div style={{ marginTop:9, paddingTop:9, borderTop:"1px dashed #e0e6e0", display:"flex", flexWrap:"wrap", gap:5 }}>
                            {fl.map(f => (
                              <span key={f.k} style={{ background:"#eef4ee", color:"#2d4a2d", borderRadius:12, padding:"2px 9px", fontWeight:700, fontSize:10.5 }}>
                                {f.icon} {f.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Usage */}
                      <div style={{ border:"1px solid #e0e6e0", borderRadius:9, padding:"11px 13px", background:"#fff" }}>
                        <div style={{ fontSize:11, fontWeight:800, color:"#60735f", textTransform:"uppercase", letterSpacing:.5, marginBottom:8 }}>
                          Usage
                        </div>
                        {(() => {
                          const s = usage[u.user_id];
                          if (!rec && !s) return <div style={{ fontSize:12, color:"#b07a20" }}>⚠ Has never signed in.</div>;
                          const cell = (label, val) => (
                            <div>
                              <div style={{ fontSize:16, fontWeight:800, color:"#1a2e1a", lineHeight:1.2 }}>{val}</div>
                              <div style={{ fontSize:10.5, color:"#60735f", fontWeight:700 }}>{label}</div>
                            </div>
                          );
                          return (
                            <>
                              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:9, marginBottom:9 }}>
                                {cell("visits, 30d", s?.sessions_30d ?? "—")}
                                {cell("days active, 30d", s?.active_days_30d ?? "—")}
                                {cell("minutes, 30d", s?.minutes_30d != null ? Math.round(s.minutes_30d) : "—")}
                              </div>
                              <div style={{ fontSize:11.5, color:"#5a6a5a", display:"flex", flexDirection:"column", gap:3 }}>
                                <div>Last active: <b style={{ color: relColor(rec) }}>{relLabel(rec)}</b></div>
                                {s?.first_seen && <div>First seen: {new Date(s.first_seen).toLocaleDateString("en-US",{ month:"short", day:"numeric", year:"numeric" })}</div>}
                                {s?.top_screen && <div>Most used: <b style={{ color:"#2d4a2d" }}>{s.top_screen}</b></div>}
                                {s?.sessions != null && <div>All time: {s.sessions} visits · {s.active_days} days</div>}
                                <div style={{ color: u.installed_at ? "#2d7a2d" : "#677167", fontWeight:600 }}>
                                  {u.installed_at
                                    ? `📱 Installed on ${u.install_platform === "ios" ? "iPhone" : u.install_platform === "android" ? "Android" : "phone"} · opened ${relLabel(u.last_standalone_at || u.installed_at)}`
                                    : "📱 Not saved to phone yet"}
                                </div>
                                {!usage[u.user_id] && rec && (
                                  <div style={{ color:"#677167", fontStyle:"italic" }}>
                                    {u.is_admin
                                      ? "Admin sessions aren't tracked — portal analytics only cover the farmer portal."
                                      : "No screen-level detail — they signed in before usage tracking, or only via email links."}
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:12 }}>
                      {onPreview && !u.is_admin && portalAccountReaches(u, activeGrants.length) && (
                        <button title="Open the portal in this user's OWN session — their RLS, their grants. What you see is what they see, including anything missing. Writes are live and are recorded in the preview audit log."
                          disabled={previewBusy === u.user_id}
                          onClick={()=>startPreview(u)}
                          style={{ ...btn("#2d6a2d"), padding:"6px 12px", opacity: previewBusy === u.user_id ? 0.6 : 1 }}>
                          {previewBusy === u.user_id ? "Opening…" : "👁 Preview"}
                        </button>
                      )}
                      {!u.no_portal_profile && (
                        <button onClick={()=>startEdit(u)} style={{ ...btn("#1a4a7a"), padding:"6px 12px" }}>Edit access</button>
                      )}
                      <button onClick={()=>resetPassword(u)} style={{ ...btn("#7a5a00"), padding:"6px 12px" }}>Reset PW</button>
                      {/* Admin logins are listed so their password can be reset, and for
                          nothing else. Revoke or delete on one locks the agency out of its
                          own dashboard — the API refuses too, this just doesn't offer it. */}
                      {!u.is_admin && (
                        <>
                          <button onClick={()=>signOutUser(u)} title="End their sessions on every device. Access unchanged — they just sign in again."
                            style={{ ...btn("#5a6a5a"), padding:"6px 12px" }}>Sign out</button>
                          {u.is_active
                            ? <button onClick={()=>setActive(u,false)} style={{ ...btn("#a06020"), padding:"6px 12px" }}>Revoke</button>
                            : <button onClick={()=>setActive(u,true)} style={{ ...btn("#2d6a2d"), padding:"6px 12px" }}>Reactivate</button>}
                          <button onClick={()=>hardDelete(u)} style={{ ...btn("#c0392b"), padding:"6px 12px" }}>Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </>
      )}
      </>
      )}
    </div>
  );
}

function AlertsManagerView({ isMobile = false }) {
  // One centralized subscriber list (email_subscribers) with per-person channel
  // flags. The legacy drp_email_subscribers name still exists as a DB view over
  // this table so the external DRP daily-email script keeps reading it.
  const [subscribers, setSubscribers] = useState([]);
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState("subs");
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [editAlertId, setEditAlertId]   = useState(null);

  async function loadAll() {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [a, subs] = await Promise.all([
        fetch(`${SUPABASE_URL}/rest/v1/coverage_alerts?order=created_at.desc`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${SUPABASE_URL}/rest/v1/email_subscribers?order=created_at.asc`, { headers }).then(r => r.ok ? r.json() : []),
      ]);
      setAlerts(a || []); setSubscribers(subs || []);
    } catch (e) { console.warn("alerts load:", e); }
    setLoading(false);
  }
  useEffect(() => { loadAll(); }, []);

  // Subscriber CRUD
  async function addSubscriber(table, payload) {
    const headers = { ...getAuthHeaders(), Prefer:"return=representation" };
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, { method:"POST", headers, body: JSON.stringify(payload) });
    if (!res.ok) { alert(`Add failed: ${await res.text()}`); return; }
    await loadAll();
  }
  async function patchSubscriber(table, id, patch) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
      method:"PATCH", headers: { ...getAuthHeaders(), Prefer:"return=minimal" }, body: JSON.stringify(patch),
    });
    if (!res.ok) { alert(`Update failed: ${await res.text()}`); return; }
    await loadAll();
  }
  async function deleteSubscriber(table, id) {
    if (!window.confirm("Delete this subscriber?")) return;
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
      method:"DELETE", headers: { ...getAuthHeaders(), Prefer:"return=minimal" },
    });
    if (!res.ok) { alert(`Delete failed: ${await res.text()}`); return; }
    await loadAll();
  }

  // Alert CRUD
  async function saveAlert(payload, id) {
    const headers = { ...getAuthHeaders(), Prefer:"return=minimal" };
    const url = id ? `${SUPABASE_URL}/rest/v1/coverage_alerts?id=eq.${id}` : `${SUPABASE_URL}/rest/v1/coverage_alerts`;
    const res = await fetch(url, { method: id ? "PATCH" : "POST", headers, body: JSON.stringify(payload) });
    if (!res.ok) { alert(`Save failed: ${await res.text()}`); return; }
    setShowAddAlert(false); setEditAlertId(null);
    await loadAll();
  }
  async function deleteAlert(id) {
    if (!window.confirm("Delete this alert?")) return;
    const res = await fetch(`${SUPABASE_URL}/rest/v1/coverage_alerts?id=eq.${id}`, {
      method:"DELETE", headers: { ...getAuthHeaders(), Prefer:"return=minimal" },
    });
    if (!res.ok) { alert(`Delete failed: ${await res.text()}`); return; }
    await loadAll();
  }

  if (loading) return <div style={{ padding:60, textAlign:"center", color:"#60735f" }}>Loading...</div>;

  const tabBtn = (k, label, count) => (
    <button onClick={() => setTab(k)} style={{
      padding: isMobile ? "10px 12px" : "10px 18px", border:"none", borderBottom: tab === k ? "3px solid #4a6e4a" : "3px solid transparent",
      background: tab === k ? "#fff" : "transparent", color: tab === k ? "#1a2e1a" : "#60735f",
      fontWeight: tab === k ? 700 : 500, fontSize: isMobile ? 13.5 : 14, cursor:"pointer", fontFamily:"inherit", flexShrink: 0,
    }}>
      {label} <span style={{ marginLeft:6, fontSize:11, color:"#677167", fontWeight:500 }}>({count})</span>
    </button>
  );

  const editingAlert = editAlertId ? alerts.find(a => a.id === editAlertId) : null;

  return (
    <div>
      <div style={{ marginBottom:14 }}>
        <h1 style={{ ...uiPageTitle(isMobile) }}>Communications</h1>
        <div style={{ fontSize:13, color:"#60735f", marginTop:4 }}>One email list for all daily digests (DRP + LRP), plus coverage-threshold alerts</div>
      </div>

      {/* Tabs — horizontal-scroll on mobile so they don't squish */}
      <div style={{ display:"flex", borderBottom:"1px solid #e0eae0", marginBottom:18, overflowX:"auto", whiteSpace:"nowrap" }}>
        {tabBtn("subs", isMobile ? "📧 Subscribers" : "📧 Email Subscribers", subscribers.length)}
        {tabBtn("alerts", isMobile ? "🎯 Alerts" : "🎯 Threshold Alerts", alerts.length)}
      </div>

      {tab === "alerts" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems: isMobile ? "stretch" : "center", marginBottom:12, gap:10, flexDirection: isMobile ? "column" : "row" }}>
            <div style={{ fontSize:12, color:"#60735f", lineHeight:1.45 }}>Alerts fire when the threshold is met during the daily sync. Cooldown prevents repeat emails within N hours.</div>
            <button onClick={() => { setEditAlertId(null); setShowAddAlert(true); }}
              style={{ padding:"8px 16px", borderRadius:6, border:"none", background:"#1a2e1a", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap", alignSelf: isMobile ? "stretch" : "flex-start" }}>+ New Alert</button>
          </div>
          {(showAddAlert || editingAlert) && (
            <AlertEditForm
              initial={editingAlert}
              isMobile={isMobile}
              onCancel={() => { setShowAddAlert(false); setEditAlertId(null); }}
              onSave={(payload) => saveAlert(payload, editingAlert?.id)}
            />
          )}
          {alerts.length === 0 ? (
            <Card style={{ textAlign:"center", padding:"40px 24px", border:"1px dashed #d8e0d0" }}>
              <div style={{ fontSize:32, marginBottom:10 }}>🎯</div>
              <div style={{ fontWeight:800, fontSize:15, color:"#1a2e1a" }}>No alerts configured yet</div>
              <div style={{ fontSize:12, color:"#60735f", marginTop:6 }}>Click &quot;+ New Alert&quot; to set a threshold for any LRP or DRP coverage value.</div>
            </Card>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {alerts.map(a => <AlertRow key={a.id} alert={a}
                isMobile={isMobile}
                onEdit={() => { setEditAlertId(a.id); setShowAddAlert(false); }}
                onDelete={() => deleteAlert(a.id)}
                onToggle={() => patchSubscriber("coverage_alerts", a.id, { active: !a.active })}
              />)}
            </div>
          )}
        </div>
      )}

      {tab === "subs" && (
        <SubscriberList table="email_subscribers" subs={subscribers} isMobile={isMobile}
          onAdd={(p) => addSubscriber("email_subscribers", p)}
          onPatch={(id, p) => patchSubscriber("email_subscribers", id, p)}
          onDelete={(id) => deleteSubscriber("email_subscribers", id)}
          digestFields={[
            { field: "send_drp_daily",    label: "DRP Daily" },
            { field: "send_lrp_ubc",      label: "UBC" },
            { field: "send_lrp_steers",   label: "Steers" },
            { field: "send_lrp_heifers",  label: "Heifers" },
            { field: "send_lrp_fed",      label: "Fed" },
          ]}
          digestNote="One list for everyone. Toggle which daily emails each person gets: the DRP daily price summary and/or LRP quote categories (UBC / Steers / Heifers / Fed). LRP quotes send each weekday afternoon as soon as RMA publishes."
          onSendTest={async (email) => {
            // BOTH digests. This button used to call only the LRP preview while
            // the note beside it said "all categories" — so a test never showed
            // the DRP email, and a missing DRP test read as a delivery failure.
            // Each result is checked on its own: one send failing must not hide
            // behind the other one succeeding.
            const q = encodeURIComponent(email);
            const sendOne = async (label, action) => {
              const res = await adminApiFetch(`/api/rma-lrp-sync?action=${action}&test=${q}`);
              const j = await res.json().catch(() => ({}));
              if (!res.ok) throw new Error(`${label}: ${j.error || `HTTP ${res.status}`}`);
              if (!(j.sent > 0)) throw new Error(`${label}: nothing sent${j.reason ? ` — ${j.reason}` : (j.errors && j.errors[0] ? ` — ${j.errors[0].error}` : "")}`);
              return j;
            };
            const results = await Promise.allSettled([sendOne("LRP", "digest"), sendOne("DRP", "drpdigest")]);
            const failed = results.filter(r => r.status === "rejected").map(r => r.reason && r.reason.message);
            if (failed.length) throw new Error(failed.join(" · "));
            return results.map(r => r.value);
          }}
          testLabel="Preview LRP + DRP emails"
        />
      )}
    </div>
  );
}

// Upcoming DRP quarters in the stored "YYYY-QN" format (matches
// drp_daily_prices.quarter). Generated from the current quarter forward so the
// dropdown never goes stale. Selecting a quarter not yet in the data simply
// yields no hits until RMA publishes it.
function drpQuarterOptions(count = 8) {
  const out = [];
  const now = new Date();
  let y = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3) + 1;
  for (let i = 0; i < count; i++) {
    out.push({ value: `${y}-Q${q}`, label: `Q${q} ${y}` });
    q++; if (q > 4) { q = 1; y++; }
  }
  return out;
}
const drpQuarterLabel = (v) => {
  const m = String(v || "").match(/^(\d{4})-Q(\d)$/);
  return m ? `Q${m[2]} ${m[1]}` : v;
};

function AlertRow({ alert, onEdit, onDelete, onToggle, isMobile = false }) {
  const fmt$ = (v) => "$" + Number(v).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
  const summary = (() => {
    const parts = [];
    parts.push(alert.commodity);
    // Grain: the BOARD is the scope. A level without its contract month is
    // meaningless — the elevator prices a delivery off one month at a time.
    if (alert.alert_type === "grain_target") {
      if (alert.contract_sym) parts.push(alert.contract_sym);
      if (alert.crop_year) parts.push(`${alert.crop_year} crop`);
      if (alert.bushels) parts.push(`${Math.round(alert.bushels).toLocaleString()} bu`);
      return parts.join(" · ");
    }
    if (alert.target_weight) parts.push(`${alert.target_weight} cwt`);
    if (alert.coverage_level) parts.push(`${Math.round(alert.coverage_level*100)}% cov`);
    if (alert.drp_quarter) parts.push(drpQuarterLabel(alert.drp_quarter));
    if (alert.end_date_filter === "month" && alert.end_date_specific) {
      parts.push(new Date(String(alert.end_date_specific).slice(0,7) + "-01T00:00:00").toLocaleDateString("en-US",{month:"short",year:"numeric"}));
    } else if (alert.end_date_filter === "specific" && alert.end_date_specific) {
      parts.push(new Date(alert.end_date_specific + "T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}));
    } else if (alert.end_date_filter === "within_n_weeks" && alert.end_date_within_weeks) {
      parts.push(`≤ ${alert.end_date_within_weeks} wks`);
    }
    return parts.join(" · ");
  })();
  const typeLbl = {
    lrp_coverage_price:"LRP Coverage $/cwt",
    lrp_coverage_per_head:"LRP Coverage $/head",
    lrp_premium:"LRP Premium $/cwt",
    lrp_premium_per_head:"LRP Premium $/head",
    drp_price:"DRP Class Price",
    drp_coverage_price:"DRP Coverage Price",
    drp_net_floor:"DRP Net Floor",
    drp_premium:"DRP Premium",
    drp_signal:"DRP Buy Signal (legacy 1–10)",
    drp_signal_legacy_v1:"DRP Buy Signal (legacy 1–10, retired)",
    grain_target:"Grain Target $/bu",
  }[alert.alert_type] || alert.alert_type;
  const isSignalRow = alert.alert_type === "drp_signal" || alert.alert_type === "drp_signal_legacy_v1";
  const isGrainRow = alert.alert_type === "grain_target";
  return (
    <div style={{ background:"#fff", border:`1px solid ${alert.active?"#c8d8c8":"#e0e0e0"}`, borderRadius:8, padding:"12px 14px", display:"flex", alignItems: isMobile ? "stretch" : "center", justifyContent:"space-between", gap:isMobile?10:12, flexWrap:"wrap", flexDirection: isMobile ? "column" : "row", opacity: alert.active ? 1 : 0.55 }}>
      <div style={{ flex:"1 1 280px", minWidth: 0 }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a", wordBreak:"break-word" }}>
          {alert.subscriber_name || alert.subscriber_email || "Agent only (office@cloverag.com)"}
          {alert.subscriber_name && <div style={{ fontSize:11, fontWeight:500, color:"#60735f", marginTop:1 }}>{alert.subscriber_email}</div>}
        </div>
        <div style={{ fontSize:12, color:"#1a2e1a", marginTop:5, lineHeight:1.45 }}>
          {typeLbl} {alert.comparison_op} <strong>{isSignalRow ? `${Number(alert.threshold_value).toFixed(1)} / 10` : fmt$(alert.threshold_value)}</strong>
          {isGrainRow && <span style={{ color:"#7a4a00", fontWeight:700, marginLeft:6 }}>· futures settle</span>}
          {alert.alert_type?.startsWith("drp_") && <span style={{ color:"#1a4a7a", fontWeight:700, marginLeft:6 }}>· {({ "36":"NY","42":"PA","26":"MI" }[alert.state_code] || alert.state_code || "NY")}</span>}
          <span style={{ color:"#60735f", marginLeft:6 }}>· {summary}</span>
        </div>
        <div style={{ fontSize:10, color:"#677167", marginTop:3 }}>
          Cooldown: {alert.cooldown_hours}h
          {alert.last_triggered_at && ` · last fired ${new Date(alert.last_triggered_at).toLocaleDateString()}`}
          {alert.notes && ` · ${alert.notes}`}
        </div>
      </div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent: isMobile ? "flex-end" : "flex-start" }}>
        <button onClick={onToggle} style={{ padding:"6px 12px", borderRadius:5, border:`1px solid ${alert.active?"#4a6e4a":"#aaa"}`, background:"#fff", color: alert.active?"#2d5a2d":"#666", fontSize:11, fontWeight:600, cursor:"pointer", flex: isMobile ? "1 1 80px" : "0 0 auto" }}>
          {alert.active ? "Active" : "Inactive"}
        </button>
        <button onClick={onEdit}   style={{ padding:"6px 12px", borderRadius:5, border:"1px solid #d0e0d0", background:"#fff", color:"#1a2e1a", fontSize:11, fontWeight:600, cursor:"pointer", flex: isMobile ? "1 1 60px" : "0 0 auto" }}>Edit</button>
        <button onClick={onDelete} style={{ padding:"6px 12px", borderRadius:5, border:"1px solid #fca5a5", background:"#fff", color:"#DC2626", fontSize:11, fontWeight:600, cursor:"pointer", flex: isMobile ? "1 1 60px" : "0 0 auto" }}>Delete</button>
      </div>
    </div>
  );
}

// `mode="portal"` renders the farmer-facing variant: the alert is owned by the
// signed-in farmer (no subscriber email/name fields), LRP is coverage-only (the
// premium metric is hidden), and cooldown/notes are admin-only so they're kept
// at their defaults and not shown. Same form, same payload shape, same eval
// engine — so a farmer alert has full metric parity with an admin one.
function AlertEditForm({ initial, onCancel, onSave, isMobile = false, mode }) {
  const portal = mode === "portal";
  const [form, setForm] = useState(() => ({
    subscriber_email: initial?.subscriber_email || "",
    subscriber_name:  initial?.subscriber_name  || "",
    alert_type:       initial?.alert_type       || "lrp_coverage_per_head",
    commodity:        initial?.commodity        || "UBC",
    target_weight:    initial?.target_weight    ?? 0.99,
    coverage_level:   initial?.coverage_level   ?? 1.00,
    end_date_filter:  initial?.end_date_filter  || "any",
    end_date_specific: initial?.end_date_specific || "",
    end_date_within_weeks: initial?.end_date_within_weeks || 26,
    drp_quarter:      initial?.drp_quarter       || "",
    // DRP premium is state-specific (drp_board carries a per-state premium).
    // LRP ignores this. FIPS: NY=36, PA=42, MI=26. Default NY (the book).
    state_code:       initial?.state_code        || "36",
    threshold_value:  initial?.threshold_value  ?? 1700,
    comparison_op:    initial?.comparison_op    || ">=",
    cooldown_hours:   initial?.cooldown_hours   ?? 24,
    notes:            initial?.notes            || "",
    active:           initial?.active ?? true,
  }));
  const set = (k, v) => setForm(f => ({...f, [k]:v}));
  const isLrp = form.alert_type.startsWith("lrp_");
  const isDrp = form.alert_type.startsWith("drp_");
  // LEGACY 1-10 composite. Retired 2026-08-28 in favour of the BUY/START/WATCH/WAIT
  // decision (src/drpDecision.js). Kept selectable ONLY so an existing row can be
  // read and turned off; a 1-10 threshold has no meaning under the new rule.
  const isSignal = form.alert_type === "drp_signal" || form.alert_type === "drp_signal_legacy_v1";
  // Valid commodity values per alert family. The previous bug: switching
  // alert_type from LRP to DRP changed the dropdown's option list, but
  // form.commodity kept the old LRP value (e.g., "UBC") which the DRP
  // alert engine then queried for in drp_daily_prices and got 0 rows.
  const LRP_COMMODITIES = ["UBC", "Steers Wt 1", "Steers Wt 2", "Heifers Wt 1", "Heifers Wt 2", "Beef-on-dairy Wt 1", "Beef-on-dairy Wt 2", "Fed Cattle"];
  const DRP_COMMODITIES = ["Class III", "Class IV"];
  // When alert_type changes, snap commodity to a valid value for the new family.
  function setAlertType(newType) {
    setForm(f => {
      const wasLrp = f.alert_type.startsWith("lrp_");
      const lrp = newType.startsWith("lrp_");
      const drp = newType.startsWith("drp_");
      const validList = lrp ? LRP_COMMODITIES : drp ? DRP_COMMODITIES : [];
      const next = { ...f, alert_type: newType };
      if (validList.length && !validList.includes(f.commodity)) {
        next.commodity = validList[0];
      }
      // DRP quotes are published only at 95% coverage (the daily-price table and
      // the quoter are both fixed there), so lock the level rather than offer
      // levels DRP can't actually price.
      if (drp) next.coverage_level = 0.95;
      // DRP doesn't use target_weight — clear it so the saved row is clean.
      if (drp) next.target_weight = null;
      // Switching product family: the old threshold default is meaningless for
      // the new family (LRP is hundreds–thousands $/head; DRP milk is ~$16–20
      // /cwt), so reset to a sane starting point for the new family.
      if (wasLrp !== lrp) next.threshold_value = lrp ? 1700 : 18;
      // Buy-signal is a 1-10 score, not a price — reset the threshold to a
      // sensible "strong" default and lock the comparison to "reaches or above".
      const wasSignal = f.alert_type === "drp_signal" || f.alert_type === "drp_signal_legacy_v1";
      if (newType === "drp_signal" || newType === "drp_signal_legacy_v1") { next.threshold_value = 8; next.comparison_op = ">="; }
      else if (wasSignal && drp) { next.threshold_value = 18; } // signal → price metric
      return next;
    });
  }
  // The Alert Type dropdown selects the *metric* (Coverage / Premium for LRP,
  // or the specific DRP metric). Per-head vs per-cwt is a separate "Threshold
  // Unit" selector. The two combine into the stored alert_type, which keeps
  // cwt/head as distinct values. uiMetric/thresholdUnit decode the canonical
  // alert_type back into the two dropdowns.
  const uiMetric = form.alert_type.startsWith("lrp_coverage") ? "lrp_coverage"
                 : form.alert_type.startsWith("lrp_premium")  ? "lrp_premium"
                 : form.alert_type;
  const thresholdUnit = form.alert_type.endsWith("_per_head") ? "head" : "cwt";
  function setMetric(metric) {
    // For LRP metrics, preserve the current unit; coming from DRP, default to
    // per-head (the value farmers think in, and what the quoter headlines).
    const wasLrp = form.alert_type.startsWith("lrp_");
    const unit = wasLrp ? thresholdUnit : "head";
    if (metric === "lrp_coverage") setAlertType(unit === "head" ? "lrp_coverage_per_head" : "lrp_coverage_price");
    else if (metric === "lrp_premium") setAlertType(unit === "head" ? "lrp_premium_per_head" : "lrp_premium");
    else setAlertType(metric); // DRP types pass through unchanged
  }
  function setUnit(unit) {
    if (form.alert_type.startsWith("lrp_coverage")) setAlertType(unit === "head" ? "lrp_coverage_per_head" : "lrp_coverage_price");
    else if (form.alert_type.startsWith("lrp_premium")) setAlertType(unit === "head" ? "lrp_premium_per_head" : "lrp_premium");
  }
  function submit(e) {
    e?.preventDefault();
    const payload = {...form};
    payload.threshold_value = Number(payload.threshold_value);
    payload.coverage_level = payload.coverage_level === "" ? null : Number(payload.coverage_level);
    payload.target_weight  = isLrp && payload.target_weight !== "" ? Number(payload.target_weight) : null;
    payload.cooldown_hours = Number(payload.cooldown_hours) || 24;
    // DRP is fixed at 95% coverage; lock it so drp_coverage_price matches the
    // quoter's "95% Floor" and the saved row is unambiguous.
    if (isDrp) payload.coverage_level = 0.95;
    // "month" reuses the end_date_specific date column (stored as the first of
    // the month, YYYY-MM-01); the backend matches any end_date in that month.
    if (payload.end_date_filter !== "specific" && payload.end_date_filter !== "month") payload.end_date_specific = null;
    if (payload.end_date_filter !== "within_n_weeks") payload.end_date_within_weeks = null;
    // Never POST "" to the date column (PostgREST rejects it) — coerce blank to null.
    if (payload.end_date_specific === "") payload.end_date_specific = null;
    // End-date scoping is LRP-only; DRP scopes by quarter instead. Keep each
    // family's row clean by nulling the other family's filter fields.
    if (isDrp) {
      payload.end_date_filter = "any";
      payload.end_date_specific = null;
      payload.end_date_within_weeks = null;
      payload.drp_quarter = payload.drp_quarter || null;
    } else {
      payload.drp_quarter = null;
    }
    // Buy-signal: score is 1-10, comparison is always "reaches or above".
    if (isSignal) {
      payload.comparison_op = ">=";
      payload.coverage_level = null;
      payload.threshold_value = Math.max(1, Math.min(10, Number(payload.threshold_value) || 8));
    }
    if (portal) {
      // Farmer alerts are owned by the signed-in user (parent attaches user_id);
      // subscriber_email/name aren't columns on price_alerts, and cooldown/notes
      // stay at their form defaults. Strip the admin-only fields, normalize notes.
      delete payload.subscriber_email;
      delete payload.subscriber_name;
      payload.notes = (payload.notes || "").trim() || null;
    }
    onSave(payload);
  }
  const inp = { padding:"6px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:13, fontFamily:"inherit" };
  const lbl = { fontSize:11, fontWeight:700, color:"#60735f", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4 };
  return (
    <form onSubmit={submit} style={{ background:"#fafcfa", border:"1px solid #c8d8c8", borderRadius:10, padding:"16px 18px", marginBottom:16 }}>
      <div style={{ fontSize:13, fontWeight:800, color:"#1a2e1a", marginBottom:10 }}>{initial ? "Edit Alert" : "New Alert"}</div>
      {!portal && (
        <>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12, marginBottom:6 }}>
            <div><div style={lbl}>Farmer Email (optional)</div><input type="email" value={form.subscriber_email} onChange={e => set("subscriber_email", e.target.value)} style={{...inp, width:"100%"}} placeholder="farmer@example.com" /></div>
            <div><div style={lbl}>Farmer Name (optional)</div><input value={form.subscriber_name} onChange={e => set("subscriber_name", e.target.value)} style={{...inp, width:"100%"}} placeholder="Mike Meisenzahl" /></div>
          </div>
          <div style={{ fontSize:11, color:"#60735f", marginBottom:10, lineHeight:1.4 }}>
            Every triggered alert is sent to <strong>office@cloverag.com</strong>{form.subscriber_email ? <> and <strong>{form.subscriber_email}</strong></> : <> too</>}. Leave the farmer email blank for a target meant just for you.
          </div>
        </>
      )}
      {portal && (
        <div style={{ fontSize:11, color:"#60735f", marginBottom:10, lineHeight:1.4 }}>
          We’ll email you (and your Clover Ag agent) the moment this target is hit. You can pause or delete it anytime.
        </div>
      )}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12, marginBottom:10 }}>
        <div>
          <div style={lbl}>Alert Type</div>
          <select value={uiMetric} onChange={e => setMetric(e.target.value)} style={{...inp, width:"100%"}}>
            <optgroup label="LRP">
              <option value="lrp_coverage">LRP Coverage</option>
              {/* Premium alerts are admin-only; farmers get coverage-price targets. */}
              {!portal && <option value="lrp_premium">LRP Premium</option>}
            </optgroup>
            <optgroup label="DRP">
              <option value="drp_price" title="The expected milk price for the quarter (the headline PRICE column on the DRP daily quotes page)">DRP Class Price</option>
              <option value="drp_coverage_price" title="The floor — Class Price × coverage level (e.g., 95% FLOOR column on the quotes page)">DRP Coverage Price</option>
              <option value="drp_net_floor" title="The guaranteed price after premium is subtracted (NET FLOOR column on the quotes page)">DRP Net Floor</option>
              {/* DRP Premium removed — premium alone isn't a useful alert
                  metric (it's just the cost, not a price/floor). */}
              {/* Admin-only experimental buy-signal alert (1-10 score). */}
              {/* RETIRED. The 1-10 composite it fires on was replaced by a decision rule
                  that emits words, not a score — see src/drpDecision.js. Selectable only so an
                  existing row can be opened and deactivated; new decision alerts are created
                  from the DRP Floor Quality screen once that signal is activated. */}
              {!portal && <option value="drp_signal_legacy_v1" title="RETIRED — the legacy DRP Buy Signal 1-10 composite. Replaced by the BUY/START/WATCH/WAIT floor-quality decision; a 1-10 threshold no longer corresponds to anything the signal produces.">DRP Buy Signal (legacy 1–10 — retired)</option>}
            </optgroup>
          </select>
          {/* Plain-language descriptions of the chosen alert, since not every
              user will hover the dropdown options to see the title. For LRP the
              per-head/per-cwt unit is set by the Threshold Unit selector. */}
          <div style={{ fontSize:11, color:"#60735f", marginTop:5, lineHeight:1.4 }}>
            {form.alert_type === "lrp_coverage_per_head" && "Triggers when LRP coverage value per head (coverage price × target weight) crosses the threshold."}
            {form.alert_type === "lrp_coverage_price"    && "Triggers when LRP coverage price per cwt crosses the threshold."}
            {form.alert_type === "lrp_premium_per_head"  && "Triggers when LRP producer premium per head (after subsidy — matches the quoter) crosses the threshold."}
            {form.alert_type === "lrp_premium"           && "Triggers when LRP producer premium per cwt (after subsidy — matches the quoter) crosses the threshold."}
            {form.alert_type === "drp_price"             && "The expected milk price for the quarter (PRICE column on the DRP quotes page)."}
            {form.alert_type === "drp_coverage_price"    && "The floor — class price × coverage level (e.g., 95% FLOOR column on the quotes page)."}
            {form.alert_type === "drp_net_floor"         && "Guaranteed price after premium is subtracted (NET FLOOR column on the quotes page)."}
            {(form.alert_type === "drp_signal" || form.alert_type === "drp_signal_legacy_v1") && "RETIRED (2026-08-28). This fired on the legacy DRP Buy Signal 1–10 composite, which has been replaced by the BUY/START/WATCH/WAIT floor-quality decision. A 1–10 threshold no longer corresponds to anything the signal produces, so this alert is inactive and is kept only so it can be reviewed and removed."}
          </div>
        </div>
        <div>
          <div style={lbl}>Commodity / Class</div>
          <select value={form.commodity} onChange={e => set("commodity", e.target.value)} style={{...inp, width:"100%"}}>
            {isLrp && <>
              <option>UBC</option>
              <option>Steers Wt 1</option><option>Steers Wt 2</option>
              <option>Heifers Wt 1</option><option>Heifers Wt 2</option>
              <option>Beef-on-dairy Wt 1</option><option>Beef-on-dairy Wt 2</option>
              <option>Fed Cattle</option>
            </>}
            {isDrp && <>
              <option>Class III</option><option>Class IV</option>
            </>}
          </select>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : (isLrp ? "1fr 1fr 1fr" : "1fr"), gap:12, marginBottom: isDrp ? 4 : 10 }}>
        {isLrp && (
          <div><div style={lbl}>Target Weight (cwt) — optional</div><input type="number" step="0.01" value={form.target_weight} onChange={e => set("target_weight", e.target.value)} style={{...inp, width:"100%"}} placeholder="0.99 for UBC" /></div>
        )}
        {/* Coverage level only applies to LRP. DRP quotes are published only at
            95% coverage, so there's no level to choose — it's locked. */}
        {isLrp && (
          <div>
            <div style={lbl}>Coverage Level (optional)</div>
            <select value={form.coverage_level} onChange={e => set("coverage_level", e.target.value)} style={{...inp, width:"100%"}}>
              <option value="">Any</option>
              <option value="1.00">100%</option>
              <option value="0.95">95%</option>
              <option value="0.90">90%</option>
              <option value="0.85">85%</option>
              <option value="0.80">80%</option>
              <option value="0.75">75%</option>
              <option value="0.70">70%</option>
            </select>
          </div>
        )}
        {/* Buy-signal fires when the score REACHES the chosen value, so the
            comparison is locked to ">=" and hidden — no need to confuse it. */}
        {!isSignal && (
          <div>
            <div style={lbl}>Comparison</div>
            <select value={form.comparison_op} onChange={e => set("comparison_op", e.target.value)} style={{...inp, width:"100%"}}>
              <option value=">=">≥ (at or above)</option>
              <option value=">">&gt; (strictly above)</option>
              <option value="<=">≤ (at or below)</option>
              <option value="<">&lt; (strictly below)</option>
            </select>
          </div>
        )}
      </div>
      {isDrp && !isSignal && (
        <div style={{ fontSize:11, color:"#60735f", marginBottom:10, lineHeight:1.4 }}>
          DRP quotes are published at <strong>95% coverage</strong> by quarter — there's no coverage level or end date to set, just the quarter below.
        </div>
      )}
      {isSignal && (
        <div style={{ fontSize:11, color:"#60735f", marginBottom:10, lineHeight:1.4 }}>
          Fires when any open quarter (of the class/state below) reaches your buy-signal score. Pick a specific quarter, or leave it on “Any quarter” to watch the whole board. Checked once daily after the signal snapshot.
        </div>
      )}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : (isLrp ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr"), gap:12, marginBottom:10 }}>
        {isSignal
          ? <div><div style={lbl}>Buy signal score (1–10)</div><input required type="number" min="1" max="10" step="0.5" value={form.threshold_value} onChange={e => set("threshold_value", e.target.value)} style={{...inp, width:"100%"}} /><div style={{ fontSize:10, color:"#677167", marginTop:3 }}>e.g. 8 = strong-lock zone</div></div>
          : <div><div style={lbl}>{isLrp ? (thresholdUnit === "head" ? "Threshold ($/head)" : "Threshold ($/cwt)") : "Threshold ($/cwt)"}</div><input required type="number" step="0.01" value={form.threshold_value} onChange={e => set("threshold_value", e.target.value)} style={{...inp, width:"100%"}} /></div>}
        {isDrp && (
          <div>
            <div style={lbl}>State</div>
            {/* DRP premium differs by state — picks which drp_board row to watch. */}
            <select value={form.state_code} onChange={e => set("state_code", e.target.value)} style={{...inp, width:"100%"}}>
              <option value="36">New York</option>
              <option value="42">Pennsylvania</option>
              <option value="26">Michigan</option>
            </select>
          </div>
        )}
        {isLrp && (
          <div>
            <div style={lbl}>Threshold Unit</div>
            <select value={thresholdUnit} onChange={e => setUnit(e.target.value)} style={{...inp, width:"100%"}}>
              <option value="head">Per head ($/head)</option>
              <option value="cwt">Per cwt ($/cwt)</option>
            </select>
          </div>
        )}
        {isLrp && <>
          <div>
            <div style={lbl}>End Date Filter</div>
            <select value={form.end_date_filter} onChange={e => set("end_date_filter", e.target.value)} style={{...inp, width:"100%"}}>
              <option value="any">Any end date</option>
              <option value="within_n_weeks">Within next N weeks</option>
              <option value="month">Specific month</option>
              <option value="specific">Specific end date</option>
            </select>
          </div>
          <div>
            {form.end_date_filter === "within_n_weeks" && (
              <><div style={lbl}>Weeks ahead</div><input type="number" value={form.end_date_within_weeks} onChange={e => set("end_date_within_weeks", e.target.value)} style={{...inp, width:"100%"}} /></>
            )}
            {form.end_date_filter === "month" && (
              // Stored as YYYY-MM-01 in end_date_specific; any end date in this
              // month at/beyond the threshold fires the alert.
              <><div style={lbl}>Month</div><input type="month" value={form.end_date_specific ? String(form.end_date_specific).slice(0,7) : ""} onChange={e => set("end_date_specific", e.target.value ? `${e.target.value}-01` : "")} style={{...inp, width:"100%"}} /></>
            )}
            {form.end_date_filter === "specific" && (
              <><div style={lbl}>End Date</div><input type="date" value={form.end_date_specific} onChange={e => set("end_date_specific", e.target.value)} style={{...inp, width:"100%"}} /></>
            )}
          </div>
        </>}
        {isDrp && (
          <div>
            <div style={lbl}>Quarter</div>
            <select value={form.drp_quarter} onChange={e => set("drp_quarter", e.target.value)} style={{...inp, width:"100%"}}>
              <option value="">Any quarter</option>
              {drpQuarterOptions().map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
            </select>
          </div>
        )}
      </div>
      {!portal && (
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12, marginBottom:10 }}>
          <div><div style={lbl}>Cooldown (hours)</div><input type="number" value={form.cooldown_hours} onChange={e => set("cooldown_hours", e.target.value)} style={{...inp, width:"100%"}} /></div>
          <div><div style={lbl}>Notes (optional)</div><input value={form.notes} onChange={e => set("notes", e.target.value)} style={{...inp, width:"100%"}} placeholder="e.g. Sparta Farms hedging strategy" /></div>
        </div>
      )}
      <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
        <button type="button" onClick={onCancel} style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #d0e0d0", background:"#fff", color:"#1a2e1a", fontWeight:600, fontSize:12, cursor:"pointer" }}>Cancel</button>
        <button type="submit" style={{ padding:"7px 16px", borderRadius:6, border:"none", background:"#1a2e1a", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>{initial ? "Save Changes" : "Create Alert"}</button>
      </div>
    </form>
  );
}

function SubscriberList({ table, subs, onAdd, onPatch, onDelete, digestField, digestLabel, digestFields, digestNote, onSendTest, isMobile = false }) {
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testNote, setTestNote] = useState(null);
  const [testing, setTesting] = useState(false);
  const inp = { padding:"6px 10px", borderRadius:6, border:"1px solid #d0e0d0", fontSize:13 };
  // Normalize the single-field (DRP) and multi-field (LRP) APIs into one list.
  const fields = digestFields || (digestField ? [{ field: digestField, label: digestLabel || "Daily digest" }] : []);
  // New-subscriber category selection — all off by default so adding someone
  // never silently subscribes them to a channel; the admin ticks what they want.
  const [addCats, setAddCats] = useState(() => fields.map(() => false));
  // DRP premium is state-specific, so a DRP-daily subscriber needs a state. Only
  // shown on lists that carry the DRP-daily category. FIPS: NY=36, PA=42, MI=26.
  const hasDrp = fields.some(f => f.field === "send_drp_daily");
  const DRP_STATE_OPTS = [["36", "New York"], ["42", "Pennsylvania"], ["26", "Michigan"]];
  const [drpState, setDrpState] = useState("36");
  function submit() {
    if (!email.trim()) return;
    const payload = { email: email.trim().toLowerCase(), name: name.trim() || null, active: true };
    fields.forEach((f, i) => { payload[f.field] = !!addCats[i]; });
    if (hasDrp) payload.drp_state = drpState;
    onAdd(payload);
    setShowAdd(false); setName(""); setEmail(""); setAddCats(fields.map(() => false)); setDrpState("36");
  }
  async function runTest() {
    if (!testEmail.trim() || !onSendTest) return;
    setTesting(true); setTestNote(null);
    try {
      await onSendTest(testEmail.trim());
      setTestNote({ ok: true, text: `Sent the LRP and DRP previews to ${testEmail.trim()}.` });
    } catch (e) { setTestNote({ ok: false, text: String(e.message || e) }); }
    setTesting(false);
  }
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems: isMobile ? "stretch" : "center", marginBottom:8, gap:10, flexDirection: isMobile ? "column" : "row" }}>
        <div style={{ fontSize:12, color:"#60735f", lineHeight:1.45 }}>{digestNote || "Email recipients for daily digests and alerts. Admin-only — no public sign-up."}</div>
        <button onClick={() => setShowAdd(s => !s)} style={{ padding:"8px 16px", borderRadius:6, border:"none", background:"#1a2e1a", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap", alignSelf: isMobile ? "stretch" : "flex-start" }}>+ Add</button>
      </div>

      {onSendTest && (
        <div style={{ background:"#f4f8fa", border:"1px solid #cfe0ec", borderRadius:8, padding:"10px 12px", marginBottom:12, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <span style={{ fontSize:12, fontWeight:700, color:"#1a4a7a" }}>Preview email:</span>
          <input placeholder="your@email.com" type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} style={{...inp, flex:"1 1 200px"}} />
          <button disabled={testing} onClick={runTest} style={{ padding:"7px 14px", borderRadius:6, border:"none", background:"#1a4a7a", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer", opacity: testing?0.6:1 }}>{testing ? "Sending…" : "Send test"}</button>
          {testNote && <span style={{ fontSize:12, color: testNote.ok ? "#2d6a1a" : "#c0392b" }}>{testNote.text}</span>}
        </div>
      )}

      {showAdd && (
        <div style={{ background:"#fafcfa", border:"1px solid #c8d8c8", borderRadius:8, padding:"12px 14px", marginBottom:12 }}>
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <input placeholder="Name (optional)" value={name} onChange={e => setName(e.target.value)} style={{...inp, flex:"1 1 160px"}} />
            <input placeholder="email@example.com" type="email" value={email} onChange={e => setEmail(e.target.value)} style={{...inp, flex:"2 1 220px"}} />
            <button onClick={submit} style={{ padding:"7px 14px", borderRadius:6, border:"none", background:"#1a2e1a", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>Save</button>
            <button onClick={() => setShowAdd(false)} style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #d0e0d0", background:"#fff", color:"#1a2e1a", fontWeight:600, fontSize:12, cursor:"pointer" }}>Cancel</button>
          </div>
          {fields.length > 0 && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:10, alignItems:"center" }}>
              <span style={{ fontSize:11, fontWeight:700, color:"#4a6e4a" }}>Daily categories:</span>
              {fields.map((f, i) => (
                <label key={f.field} style={{ fontSize:12, fontWeight:600, color:"#2d4a2d", display:"flex", alignItems:"center", gap:5, cursor:"pointer" }}>
                  <input type="checkbox" checked={!!addCats[i]} onChange={e => setAddCats(c => c.map((v, j) => j === i ? e.target.checked : v))} />
                  {f.label}
                </label>
              ))}
              {hasDrp && (
                <label style={{ fontSize:12, fontWeight:600, color:"#2d4a2d", display:"flex", alignItems:"center", gap:5 }}>
                  DRP state:
                  <select value={drpState} onChange={e => setDrpState(e.target.value)} style={{...inp, padding:"4px 8px"}}>
                    {DRP_STATE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
              )}
            </div>
          )}
        </div>
      )}
      {subs.length === 0 ? (
        <Card style={{ textAlign:"center", padding:"30px 24px", border:"1px dashed #d8e0d0" }}>
          <div style={{ fontSize:24, marginBottom:8 }}>📧</div>
          <div style={{ fontSize:13, color:"#60735f" }}>No subscribers yet</div>
        </Card>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {subs.map(s => {
            const onCats = fields.filter(f => s[f.field]).map(f => f.label);
            return (
            <div key={s.id} style={{ display:"flex", alignItems: isMobile ? "stretch" : "center", justifyContent:"space-between", gap:isMobile?10:10, padding:"10px 14px", background:"#fff", border:"1px solid #e0eae0", borderRadius:8, opacity: s.active ? 1 : 0.55, flexDirection: isMobile ? "column" : "row", flexWrap:"wrap" }}>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a", wordBreak:"break-word" }}>{s.name || s.email}</div>
                <div style={{ fontSize:11, color:"#60735f", wordBreak:"break-word" }}>{s.email}{onCats.length ? ` · ${onCats.join(" · ")}` : (fields.length ? " · no categories on" : "")}</div>
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent: isMobile ? "flex-end" : "flex-start" }}>
                <button onClick={() => onPatch(s.id, { active: !s.active })} style={{ padding:"6px 12px", borderRadius:5, border:`1px solid ${s.active?"#4a6e4a":"#aaa"}`, background:"#fff", color: s.active?"#2d5a2d":"#666", fontSize:11, fontWeight:600, cursor:"pointer", flex: isMobile ? "1 1 80px" : "0 0 auto" }}>{s.active ? "Active" : "Inactive"}</button>
                {fields.map(f => (
                  <button key={f.field} onClick={() => onPatch(s.id, { [f.field]: !s[f.field] })} style={{ padding:"6px 11px", borderRadius:5, border:`1px solid ${s[f.field]?"#1a4a8a":"#d0e0d0"}`, background: s[f.field]?"#eaf2fb":"#fff", color: s[f.field]?"#1a4a8a":"#888", fontSize:11, fontWeight:700, cursor:"pointer", flex: isMobile ? "1 1 70px" : "0 0 auto" }}>
                    {s[f.field] ? "✓ " : ""}{f.label}
                  </button>
                ))}
                {hasDrp && s.send_drp_daily && (
                  // Which state's DRP premium this subscriber's daily email shows.
                  <select value={s.drp_state || "36"} onChange={e => onPatch(s.id, { drp_state: e.target.value })} title="DRP state for this subscriber's daily email" style={{ padding:"5px 8px", borderRadius:5, border:"1px solid #1a4a8a", background:"#eaf2fb", color:"#1a4a8a", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                    {DRP_STATE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                )}
                <button onClick={() => onDelete(s.id)} style={{ padding:"6px 12px", borderRadius:5, border:"1px solid #fca5a5", background:"#fff", color:"#DC2626", fontSize:11, fontWeight:600, cursor:"pointer", flex: isMobile ? "1 1 60px" : "0 0 auto" }}>Delete</button>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

// Farmer-facing price alerts. Owner-scoped via RLS (price_alerts_owner_all:
// user_id = auth.uid()), so a farmer only ever sees/edits their own. Full metric
// parity with the admin alerts via the shared AlertEditForm (mode="portal"):
// DRP price / coverage / net floor by quarter, and LRP coverage ($/cwt and
// $/head) for any cattle type with end-date / month / within-N-weeks scoping.
// The firing engine (rma-lrp-sync checkAndFirePriceAlerts) reuses the admin eval
// logic and emails the owner + agency when a threshold is crossed.
const PORTAL_ALERT_TYPE_LBL = {
  lrp_coverage_price:    "LRP Coverage $/cwt",
  lrp_coverage_per_head: "LRP Coverage $/head",
  drp_price:             "DRP Class Price",
  drp_coverage_price:    "DRP Coverage Price",
  drp_net_floor:         "DRP Net Floor",
  grain_target:          "Grain Target $/bu",
};
// One-line scope summary for an alert card (commodity + any active filters),
// mirroring the admin AlertRow summary. drpQuarterLabel is module-scope.
function portalAlertScope(a) {
  const parts = [a.commodity];
  // Grain scopes by BOARD, never by state/quarter/weight — a $/bu level only means
  // something attached to the contract month the resting order sits on.
  if (a.alert_type === "grain_target") {
    if (a.contract_sym) parts.push(a.contract_sym);
    if (a.crop_year) parts.push(`${a.crop_year} crop`);
    if (a.bushels) parts.push(`${Math.round(a.bushels).toLocaleString()} bu`);
    return parts.join(" · ");
  }
  if (a.alert_type?.startsWith("drp_")) parts.push({ "36":"NY","42":"PA","26":"MI" }[a.state_code] || a.state_code || "NY");
  if (a.target_weight) parts.push(`${a.target_weight} cwt`);
  if (a.coverage_level) parts.push(`${Math.round(a.coverage_level * 100)}% cov`);
  if (a.drp_quarter) parts.push(drpQuarterLabel(a.drp_quarter));
  if (a.end_date_filter === "month" && a.end_date_specific) {
    parts.push(new Date(String(a.end_date_specific).slice(0, 7) + "-01T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }));
  } else if (a.end_date_filter === "specific" && a.end_date_specific) {
    parts.push(new Date(a.end_date_specific + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }));
  } else if (a.end_date_filter === "within_n_weeks" && a.end_date_within_weeks) {
    parts.push(`≤ ${a.end_date_within_weeks} wks`);
  }
  return parts.join(" · ");
}
function PortalAlertsView({ farmers = [], isMobile = false }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // null = list view · {} = new · {row} = edit
  // fmt$ is component-local throughout App.jsx (not module-scope), so define a
  // tiny self-contained threshold formatter here.
  const fmtThresh = (v) => v == null ? "—" : "$" + Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  async function load() {
    setLoading(true); setErr(null);
    try {
      // Narrow by user_id AT THE QUERY, never on RLS alone. price_alerts carries
      // TWO policies — price_alerts_owner_all (user_id = auth.uid()) and
      // price_alerts_admin_all (is_admin()). For a real farmer the owner policy
      // is the only one that applies and this filter is a no-op. But "Preview as"
      // renders the portal with an ADMIN token, and under that token the admin
      // policy correctly returns EVERY row in the table — so a preview of any
      // farmer showed whatever alerts other users had created. Same shape as the
      // JD Compare picker: hide the surface, then guard the fetch.
      const uid = getCurrentUserId();
      if (!uid) { setAlerts([]); setLoading(false); return; }
      const r = await fetch(`${SUPABASE_URL}/rest/v1/price_alerts?select=*&user_id=eq.${uid}&order=created_at.desc`, { headers: getAuthHeaders() });
      // A non-2xx used to fall to [] silently, which renders — and logs — as
      // "this farmer has set no alerts". Say the status instead.
      if (!r.ok) { setAlerts([]); setErr(`price_alerts ${r.status}`); }
      else setAlerts(await r.json());
    } catch (e) { setErr(String(e.message || e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);
  usePortalEmptyState("alerts", alerts.length === 0, !loading, err);

  // AlertEditForm (mode="portal") emits the same payload shape as the admin
  // form, minus the admin-only fields it strips. We attach ownership (user_id /
  // farmer_id) and write to the RLS-scoped price_alerts table.
  async function save(payload) {
    const uid = getCurrentUserId();
    if (!uid) { setErr("Your session expired — please sign out and back in."); return; }
    setErr(null);
    const body = {
      ...payload,
      user_id: uid,
      farmer_id: farmers.length === 1 ? farmers[0].id : (editing?.farmer_id ?? null),
      active: editing?.active ?? true,
    };
    const id = editing?.id;
    const url = id ? `${SUPABASE_URL}/rest/v1/price_alerts?id=eq.${id}` : `${SUPABASE_URL}/rest/v1/price_alerts`;
    const r = await fetch(url, { method: id ? "PATCH" : "POST", headers: { ...getAuthHeaders(), Prefer: "return=minimal" }, body: JSON.stringify(body) });
    if (!r.ok) { setErr(await r.text()); return; }
    trackPortalAction("set_alert", { screen: "alerts", mode: id ? "edit" : "new", commodity: payload?.commodity || null });
    setEditing(null); await load();
  }
  async function toggle(a) {
    await fetch(`${SUPABASE_URL}/rest/v1/price_alerts?id=eq.${a.id}`, {
      method: "PATCH", headers: { ...getAuthHeaders(), Prefer: "return=minimal" }, body: JSON.stringify({ active: !a.active }),
    });
    await load();
  }
  async function remove(a) {
    if (!window.confirm("Delete this alert?")) return;
    await fetch(`${SUPABASE_URL}/rest/v1/price_alerts?id=eq.${a.id}`, { method: "DELETE", headers: { ...getAuthHeaders(), Prefer: "return=minimal" } });
    await load();
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom:6, flexWrap:"wrap" }}>
        <h1 style={{ ...uiPageTitle(isMobile) }}>Price Alerts</h1>
        {!editing && (
          <button onClick={()=>setEditing({})} style={{ padding:"9px 18px", background:"#2d6a2d", color:"#fff", border:"none", borderRadius:8, fontWeight:800, fontSize:13, cursor:"pointer" }}>+ New alert</button>
        )}
      </div>
      <p style={{ color:"#5a6a5a", fontSize:13, marginTop:0, marginBottom:18 }}>
        Get an email the moment a price hits your target — DRP Class III/IV (price, coverage, or net floor) or LRP coverage for any cattle type.
      </p>
      {err && <div style={{ background:"#fdecea", color:"#c0392b", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:14, whiteSpace:"pre-wrap", wordBreak:"break-word" }}>{err}</div>}

      {editing ? (
        <AlertEditForm
          mode="portal"
          isMobile={isMobile}
          initial={editing.id ? editing : null}
          onCancel={()=>{ setEditing(null); setErr(null); }}
          onSave={save}
        />
      ) : loading ? (
        <div style={{ textAlign:"center", padding:30, color:"#60735f" }}>Loading…</div>
      ) : alerts.length === 0 ? (
        <div style={{ textAlign:"center", padding:30, color:"#677167", fontSize:14 }}>No alerts yet. Tap “New alert” to set one.</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {alerts.map(a => (
            <div key={a.id} style={{ display:"flex", alignItems: isMobile ? "stretch" : "center", justifyContent:"space-between", gap:12, border:"1px solid #e0e6e0", borderRadius:8, padding:"11px 14px", background:a.active?"#fff":"#f6f6f6", flexDirection: isMobile ? "column" : "row", opacity:a.active?1:0.6 }}>
              <div style={{ minWidth:0 }}>
                <div style={{ fontWeight:700, color:"#1a2e1a", fontSize:14 }}>
                  {PORTAL_ALERT_TYPE_LBL[a.alert_type] || a.alert_type} {a.comparison_op} <strong>{fmtThresh(a.threshold_value)}</strong>
                  {!a.active && <span style={{ fontSize:11, color:"#a06020", fontWeight:700, marginLeft:8 }}>· paused</span>}
                </div>
                <div style={{ fontSize:12, color:"#60735f", marginTop:3 }}>{portalAlertScope(a)}</div>
                {a.alert_type === "grain_target" && (
                  <div style={{ fontSize:11, color:"#677167", marginTop:3 }}>
                    Set from the Marketing Plan{a.last_fired_at ? ` · last hit ${new Date(a.last_fired_at).toLocaleDateString()}` : ""}
                  </div>
                )}
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", justifyContent: isMobile ? "flex-end" : "flex-start" }}>
                {/* Grain targets are BOARD-scoped and created off a plan rung; this
                    form is LRP/DRP-shaped and would strip contract_sym/bushels on
                    save. Edit them where they're made — pause/delete work fine here. */}
                {a.alert_type !== "grain_target" && (
                  <button onClick={()=>setEditing(a)} style={{ padding:"5px 12px", background:"#fff", color:"#1a2e1a", border:"1px solid #d0e0d0", borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer" }}>Edit</button>
                )}
                <button onClick={()=>toggle(a)} style={{ padding:"5px 12px", background:a.active?"#a06020":"#2d6a2d", color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer" }}>{a.active?"Pause":"Resume"}</button>
                <button onClick={()=>remove(a)} style={{ padding:"5px 12px", background:"#c0392b", color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer" }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Logged-in portal user changes their own password (Supabase PUT /auth/v1/user
// with their session token). Self-contained: renders a small button + modal.
function PortalChangePassword({ inline } = {}) {
  // ⚠ NEVER WHILE PREVIEWING. getAuthHeaders() sends the impersonated FARMER's
  // token during "Preview as farmer", so a password set here would change that
  // farmer's real password. The control is hidden, and submit() refuses too.
  const previewing = isPreviewSession();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  async function submit(e) {
    e.preventDefault();
    if (isPreviewSession()) { setMsg({ err: true, t: "Passwords can't be changed while previewing a farmer." }); return; }
    if (pw.length < 8) { setMsg({ err: true, t: "Use at least 8 characters." }); return; }
    if (pw !== pw2) { setMsg({ err: true, t: "Passwords don't match." }); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, { method: "PUT", headers: getAuthHeaders(), body: JSON.stringify({ password: pw }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.msg || j.error_description || "Could not change password"); }
      setMsg({ err: false, t: "Password updated." }); setPw(""); setPw2("");
      setTimeout(() => { setOpen(false); setMsg(null); }, 1400);
    } catch (e) { setMsg({ err: true, t: String(e.message || e) }); }
    setBusy(false);
  }
  if (previewing) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} style={inline
        ? { background: "#2d4a2d", border: "none", borderRadius: 6, padding: "6px 10px", color: "#a8d878", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }
        : { width: "100%", background: "transparent", border: "1px solid #2d4a2d", borderRadius: 6, padding: "7px", color: "#a8d878", fontSize: 11, fontWeight: 700, cursor: "pointer", marginBottom: 8 }}>
        {inline ? "🔑 Password" : "Change password"}
      </button>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <form onClick={e => e.stopPropagation()} onSubmit={submit} style={{ background: "#fff", borderRadius: 12, padding: "24px 22px", width: "100%", maxWidth: 360, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#1a2e1a", marginBottom: 14 }}>Change password</div>
            {msg && <div style={{ background: msg.err ? "#fdecea" : "#e6f4e0", color: msg.err ? "#c0392b" : "#2d6a1a", padding: "9px 12px", borderRadius: 7, fontSize: 12, marginBottom: 12 }}>{msg.t}</div>}
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="New password (8+ characters)" autoFocus style={{ width: "100%", padding: "10px 12px", borderRadius: 7, border: "1px solid #c8d8c8", fontSize: 14, boxSizing: "border-box", marginBottom: 10 }} />
            <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Confirm new password" style={{ width: "100%", padding: "10px 12px", borderRadius: 7, border: "1px solid #c8d8c8", fontSize: 14, boxSizing: "border-box", marginBottom: 14 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" disabled={busy} style={{ flex: 1, padding: "10px", background: "#2d6a2d", color: "#fff", border: "none", borderRadius: 7, fontWeight: 800, fontSize: 13, cursor: "pointer", opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : "Update"}</button>
              <button type="button" onClick={() => setOpen(false)} style={{ padding: "10px 16px", background: "#e4ece4", color: "#2d4a2d", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

// Shown after a user follows an invite / recovery magic link. The session is
// already adopted from the URL hash; here they choose a password.
function SetPasswordScreen({ onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  async function submit(e) {
    e.preventDefault();
    if (pw.length < 8) { setErr("Use at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT", headers: getAuthHeaders(), body: JSON.stringify({ password: pw }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.msg || j.error_description || "Could not set password"); }
      onDone();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  }
  const inp = { width:"100%", padding:"10px 12px", borderRadius:7, border:"1px solid #c8d8c8", fontSize:14, boxSizing:"border-box", marginBottom:12 };
  return (
    <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", padding:24, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <form onSubmit={submit} style={{ width:"100%", maxWidth:380, background:"#fff", borderRadius:12, padding:"32px 28px", border:"1px solid #d8e0d8" }}>
        <img src="/silas-wordmark-dark.svg" alt="SILAS" style={{ width:158, height:42, display:"block", margin:"0 auto 5px" }} />
          <div style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:"#7aaa7a", textTransform:"uppercase", textAlign:"center", marginBottom:6 }}>by Clover Ag Risk Management</div>
        <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a", marginBottom:4, textAlign:"center" }}>Welcome — set your password</div>
        <div style={{ fontSize:12, color:"#6a7a6a", marginBottom:20, textAlign:"center" }}>Create a password to finish setting up your account.</div>
        {err && <div style={{ background:"#fdecea", color:"#c0392b", padding:"9px 12px", borderRadius:7, fontSize:12, marginBottom:12 }}>{err}</div>}
        <input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="New password" autoFocus style={inp} />
        <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)} placeholder="Confirm password" style={inp} />
        <button type="submit" disabled={busy} style={{ width:"100%", padding:"11px", background:"#2d6a2d", color:"#fff", border:"none", borderRadius:7, fontWeight:800, fontSize:14, cursor:"pointer", opacity:busy?0.6:1 }}>
          {busy ? "Saving…" : "Set password & continue"}
        </button>
      </form>
    </div>
  );
}

// Loads the signed-in (non-admin) user's portal profile + grants, then renders
// the farmer Portal. RLS scopes both queries to the current user's own rows.
function PortalGate() {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let cancelled = false;

    // Re-checked, not read once. Maintenance mode used to be evaluated a single
    // time on mount, so a phone with the app already open never saw it — the
    // switch was flipped and that device kept browsing until it happened to
    // reload. Same for a revoke. Now the gate re-reads whenever the app comes
    // back to the foreground and on a slow timer while it's open, so "pause the
    // portal" reaches a device that never reloaded.
    //
    // Only a MATERIAL change re-renders (see applyState) — an unchanged re-check
    // returns the previous state object, so the portal underneath doesn't churn
    // every two minutes.
    const applyState = (next) => setState(prev => {
      if (!prev.loading && !prev.authError
          && prev.portalDisabled === next.portalDisabled
          && prev.portalMessage === next.portalMessage
          && !!(prev.portalUser && prev.portalUser.is_active) === !!(next.portalUser && next.portalUser.is_active)
          && (prev.grants || []).length === (next.grants || []).length) {
        return prev;
      }
      return next;
    });

    async function check(initial) {
      try {
        // authedFetch, NOT a raw fetch: these three are the first calls a farmer
        // makes after signing in, and on a stale JWT a raw fetch 401s silently →
        // pu=null → the "No access yet" card below. That card tells a paying
        // customer their account isn't set up, when all that happened was a
        // token that needed refreshing. authedFetch refreshes and retries.
        const [puRes, grRes, cfgRes] = await Promise.all([
          authedFetch(`${SUPABASE_URL}/rest/v1/portal_users?select=*`),
          authedFetch(`${SUPABASE_URL}/rest/v1/farmer_portal_users?select=farmer_id,is_active,permissions`),
          authedFetch(`${SUPABASE_URL}/rest/v1/app_config?select=portal_enabled,portal_message&id=eq.1`),
        ]);
        // If the profile read still comes back unauthorized after that retry, the
        // session is the problem — say so, and never mistake it for "no grants".
        // This also fires on a RE-check, which is how "Sign everyone out" reaches
        // a phone that never reloaded: the session row is gone, so the silent
        // refresh fails and this card replaces whatever was on screen.
        if (puRes.status === 401 || puRes.status === 403) {
          noteAuthEvent("portal-gate-401", `portal_users read returned ${puRes.status} after refresh+retry`);
          if (!cancelled) setState({ loading: false, authError: true });
          return;
        }
        const pu = puRes.ok ? ((await puRes.json())[0] || null) : null;
        const grantsAll = grRes.ok ? await grRes.json() : [];
        // Fail OPEN on a config read error. If app_config is unreachable we'd rather
        // serve the portal than lock every farmer out over a transient blip — the
        // hard kill (mass revoke) is the tier that must fail closed, and it does,
        // because it's enforced by RLS rather than by this fetch.
        const cfg = cfgRes.ok ? ((await cfgRes.json())[0] || null) : null;
        if (!cancelled) applyState({
          loading: false,
          portalUser: pu,
          grants: grantsAll.filter(g => g.is_active !== false),
          portalDisabled: cfg ? cfg.portal_enabled === false : false,
          portalMessage: cfg?.portal_message || null,
        });
      } catch (e) {
        // A failed RE-check must never blank a working portal — a dropped
        // signal on a tractor is not a reason to show an error card. Only the
        // first load can surface one.
        if (initial && !cancelled) setState({ loading: false, error: String(e.message || e) });
      }
    }

    check(true);
    const recheck = () => { if (typeof document === "undefined" || document.visibilityState === "visible") check(false); };
    // Foreground is the important one: a phone that's been in a pocket for a day
    // re-checks the moment it's opened, without needing a full app relaunch.
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", recheck);
    const timer = setInterval(recheck, 120000);

    return () => {
      cancelled = true;
      clearInterval(timer);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", recheck);
    };
  }, []);

  if (state.loading) {
    return (
      <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        <div style={{ textAlign:"center" }}>
          <img src="/silas-wordmark-dark.svg" alt="SILAS" style={{ width:158, height:42, display:"block", margin:"0 auto 5px" }} />
          <div style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:"#7aaa7a", textTransform:"uppercase", textAlign:"center", marginBottom:8 }}>by Clover Ag Risk Management</div>
          <div style={{ fontSize:11, color:"#60735f", letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:700 }}>Loading…</div>
        </div>
      </div>
    );
  }

  // ── Expired session ───────────────────────────────────────────────────────
  // The profile read came back 401/403 even after a refresh + retry. That is a
  // session problem, not a permissions problem, and it must NOT fall through to
  // the "No access yet" card — telling a customer their account isn't set up
  // when their token merely lapsed is how a five-second re-login turns into a
  // phone call to the office.
  if (state.authError) {
    const relogin = () => { try { supabaseSignOut(); } catch (e) {} window.location.reload(); };
    return (
      <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", padding:24, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        <div style={{ textAlign:"center", maxWidth:440, background:"#fff", borderRadius:12, padding:"36px 28px", border:"1px solid #d8e0d8" }}>
          <img src="/silas-wordmark-dark.svg" alt="SILAS" style={{ width:158, height:42, display:"block", margin:"0 auto 5px" }} />
          <div style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:"#7aaa7a", textTransform:"uppercase", textAlign:"center", marginBottom:14 }}>by Clover Ag Risk Management</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#1a2e1a", marginBottom:8 }}>Your session expired</div>
          <div style={{ fontSize:13, color:"#5a6a5a", lineHeight:1.55, marginBottom:20 }}>
            Signing in again will pick up right where you left off. Your coverage and everything in your account are unchanged.
          </div>
          <button onClick={relogin} style={{ padding:"10px 24px", background:"#2d6a2d", color:"#fff", border:"none", borderRadius:7, fontWeight:800, fontSize:13, cursor:"pointer" }}>
            Sign in again
          </button>
          <div style={{ fontSize:12, color:"#60735f", marginTop:16 }}>
            Still stuck? <a href="mailto:office@cloverag.com" style={{ color:"#2d6a2d" }}>office@cloverag.com</a> or (585) 233-1066.
          </div>
        </div>
      </div>
    );
  }

  // ── Soft kill switch ──────────────────────────────────────────────────────
  // app_config.portal_enabled=false → farmers see a maintenance card instead of
  // the app. Grants are untouched, so flipping it back restores everyone exactly
  // as they were. This is the one to use for "we shipped a bug" or "mid-migration".
  // It is a UI gate, NOT a security boundary — see portal_revoke_all() for that.
  if (state.portalDisabled) {
    const logout = () => { try { supabaseSignOut(); } catch (e) {} window.location.reload(); };
    return (
      <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", padding:24, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        <div style={{ textAlign:"center", maxWidth:440, background:"#fff", borderRadius:12, padding:"36px 28px", border:"1px solid #d8e0d8" }}>
          <img src="/silas-wordmark-dark.svg" alt="SILAS" style={{ width:158, height:42, display:"block", margin:"0 auto 5px" }} />
          <div style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:"#7aaa7a", textTransform:"uppercase", textAlign:"center", marginBottom:14 }}>by Clover Ag Risk Management</div>
          <div style={{ fontSize:30, marginBottom:10 }}>🔧</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#1a2e1a", marginBottom:8 }}>Down for maintenance</div>
          <div style={{ fontSize:13, color:"#5a6a5a", lineHeight:1.55, marginBottom:20 }}>
            {state.portalMessage
              || "We’re making some updates and the portal is briefly unavailable. Please check back shortly — your coverage is unaffected."}
          </div>
          <div style={{ fontSize:12, color:"#60735f", marginBottom:18 }}>
            Need something now? Contact your agent at <a href="mailto:office@cloverag.com" style={{ color:"#2d6a2d" }}>office@cloverag.com</a> or (585) 233-1066.
          </div>
          <button onClick={() => window.location.reload()} style={{ padding:"9px 20px", background:"#2d4a2d", color:"#fff", border:"none", borderRadius:7, fontWeight:800, fontSize:12, cursor:"pointer", marginRight:8 }}>
            Check again
          </button>
          <button onClick={logout} style={{ padding:"9px 20px", background:"#fff", color:"#5a6a5a", border:"1px solid #d8e0d8", borderRadius:7, fontWeight:700, fontSize:12, cursor:"pointer" }}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const pu = state.portalUser;
  const active = pu && pu.is_active !== false;
  const canViewMi = !!(pu && pu.can_view_mi);
  const canViewElevator = !!(pu && pu.can_view_elevator);
  const canSetAlerts = !!(pu && pu.can_set_alerts);
  const canView578 = !!(pu && pu.can_view_578);
  // The EXPLICIT book-wide 578 permission (sql/069). Never inferred from the flag
  // or the role; null/absent before the column existed reads false.
  const bookWide578 = !!(pu && pu.can_view_578 && pu.book_wide_578);
  const canViewModelFarm = !!(pu && pu.can_view_model_farm);
  const canViewCashTargets = !!(pu && pu.can_view_cash_targets);
  // AGENCY class like the cash-target grant: the 426 names a grower, so it rides its
  // own column and never a page tick. Absent column reads false.
  const canView426 = !!(pu && pu.can_view_426);
  // A limited staff member may hold NO farm grants at all — the cash-target
  // grant, or a page allowlist naming only open pages, is their whole account.
  // Without these two they would be shown "your account isn't set up to view any
  // coverage yet", which is the No-access card telling somebody with a working
  // grant that they have none.
  const hasAccess = portalAccountReaches(pu, (state.grants || []).length);

  if (!hasAccess) {
    const logout = () => { try { supabaseSignOut(); } catch (e) {} window.location.reload(); };
    return (
      <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", padding:24, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        <div style={{ textAlign:"center", maxWidth:440, background:"#fff", borderRadius:12, padding:"36px 28px", border:"1px solid #d8e0d8" }}>
          <img src="/silas-wordmark-dark.svg" alt="SILAS" style={{ width:158, height:42, display:"block", margin:"0 auto 5px" }} />
          <div style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:"#7aaa7a", textTransform:"uppercase", textAlign:"center", marginBottom:10 }}>by Clover Ag Risk Management</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#1a2e1a", marginBottom:8 }}>No access yet</div>
          <div style={{ fontSize:13, color:"#5a6a5a", lineHeight:1.5, marginBottom:20 }}>
            Your account ({getCurrentUserEmail()}) isn’t set up to view any coverage yet. Please contact your Clover Ag agent at <a href="mailto:office@cloverag.com" style={{ color:"#2d6a2d" }}>office@cloverag.com</a> or (585) 233-1066.
          </div>
          <button onClick={logout} style={{ background:"#2d4a2d", border:"none", borderRadius:7, padding:"9px 22px", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>Sign out</button>
        </div>
      </div>
    );
  }

  // marketTabs: NULL/absent = follow coverage. Passed through as-is so the
  // "unset" case stays distinguishable from "set to an empty list" (= no markets).
  return <Dashboard portalMode={{ grants: state.grants, canViewMi, canViewElevator, canSetAlerts, canView578, bookWide578, canViewModelFarm, canViewCashTargets, canView426, marketTabs: Array.isArray(pu.market_tabs) ? pu.market_tabs : null, pageAccess: Array.isArray(pu.page_access) ? pu.page_access : null, role: pu.role || null, displayName: pu.display_name || pu.email, homeLat: pu.home_lat, homeLon: pu.home_lon, homeLabel: pu.home_label }} />;
}

function App() {
  // Tri-state auth: "loading" while we restore + refresh the persisted session,
  // "authed" once the JWT is fresh, "unauthed" if there's no session or the
  // refresh_token is dead. Dashboard does NOT mount during "loading" — that's
  // what prevents the empty-on-first-load bug where data fetches raced ahead
  // of the token refresh and got rejected by Supabase.
  const [authStatus, setAuthStatus] = useState(() =>
    localStorage.getItem("cloverag_access_token") ? "loading" : "unauthed"
  );
  const [inviteFlow, setInviteFlow] = useState(false);

  // Catch the tokens GoTrue leaves in the URL hash after an invite / recovery
  // magic link, adopt the session, and route the user to set a password.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    if (!hash.includes("access_token")) return;
    const p = new URLSearchParams(hash.replace(/^#/, ""));
    const at = p.get("access_token");
    if (!at) return;
    setSupabaseTokens(at, p.get("refresh_token"), parseInt(p.get("expires_in") || "3600", 10));
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    const type = p.get("type");
    if (type === "invite" || type === "recovery") setInviteFlow(true);
    setAuthStatus("authed");
  }, []);

  useEffect(() => {
    if (authStatus !== "loading") return;
    let cancelled = false;
    (async () => {
      const ok = await restoreSession();
      if (!cancelled) setAuthStatus(ok ? "authed" : "unauthed");
    })();
    return () => { cancelled = true; };
  }, [authStatus]);
  // Let the mobile-preview frame (App's parent) re-evaluate admin status when auth
  // resolves, so its toggle appears immediately after a fresh login.
  useEffect(() => {
    if (authStatus === "authed") { try { window.dispatchEvent(new Event("cloverag:auth")); } catch (e) {} }
  }, [authStatus]);
  // Load Google Fonts (Lora + Plus Jakarta Sans)
  useEffect(() => {
    if (!document.getElementById('cloverag-fonts')) {
      const link = document.createElement('link');
      link.id = 'cloverag-fonts';
      link.rel = 'stylesheet';
      link.href = "https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;0,700;1,400&family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=Source+Sans+3:wght@400;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);
  if (authStatus === "loading") {
    return (
      <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans','Helvetica Neue',sans-serif" }}>
        <div style={{ textAlign:"center" }}>
          <img src="/silas-wordmark-dark.svg" alt="SILAS" style={{ width:158, height:42, display:"block", margin:"0 auto 5px" }} />
          <div style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:"#7aaa7a", textTransform:"uppercase", textAlign:"center", marginBottom:8 }}>by Clover Ag Risk Management</div>
          <div style={{ fontSize:11, color:"#60735f", letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:700 }}>Loading…</div>
        </div>
      </div>
    );
  }
  if (inviteFlow) return <SetPasswordScreen onDone={() => setInviteFlow(false)} />;
  if (authStatus === "unauthed") return <LoginScreen onLogin={() => setAuthStatus("authed")} />;
  // Admins → full agent Dashboard. Everyone else → scoped farmer Portal.
  // (RLS is the real gate; this only picks which shell to render.)
  //
  // isCurrentUserAdmin() is the EFFECTIVE identity, so it is false during
  // preview — which is what makes admin-only affordances disappear from shared
  // components, exactly as they do for the farmer. But the shell itself must
  // stay the admin's: Dashboard is what owns the orange preview frame, the Exit
  // button, and the `!isPreview` guards that keep PortalUsageTracker from
  // writing the FARMER's analytics while we look around. So route on the real
  // session, and let the effective identity govern everything inside.
  if (isRealAdminSession() || isPreviewSession()) return <Dashboard />;
  return <PortalGate />;
}
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, stack: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error("App crash:", error, info);
    this.setState({ stack: info?.componentStack || null });
    // Report it — a farmer who white-screens on their phone will not call us.
    reportClientError("boundary", error, { componentStack: info?.componentStack });
  }
  render() {
    if (this.state.error) {
      // Farmers get a plain apology; only admins see the stack. A raw trace is
      // noise to a farmer and can leak internals, and we now capture it server-side
      // anyway — nobody needs to read it off a phone screen and text it to us.
      const isAdmin = isCurrentUserAdmin();
      return (
        <div style={{ minHeight:"100vh", background:"#f2f6f2", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans','Helvetica Neue',sans-serif" }}>
          <div style={{ textAlign:"center", maxWidth:640, padding:32 }}>
            <div style={{ fontSize:32, marginBottom:12 }}>{isAdmin ? "💥" : "🌾"}</div>
            <div style={{ fontSize:16, fontWeight:800, color: isAdmin ? "#c0392b" : "#1a2e1a", marginBottom:12 }}>
              {isAdmin ? "Something went wrong" : "This page didn’t load"}
            </div>
            {isAdmin ? (
              <>
                <div style={{ fontSize:12, fontVariantNumeric: "tabular-nums", color:"#c0392b", background:"#fdecea", padding:"12px 16px", borderRadius:8, textAlign:"left", marginBottom:8, wordBreak:"break-all" }}>
                  {this.state.error?.message || String(this.state.error)}
                </div>
                {this.state.stack && (
                  <div style={{ fontSize:10, fontVariantNumeric: "tabular-nums", color:"#888", background:"#f8f8f8", border:"1px solid #ddd", padding:"10px 14px", borderRadius:6, textAlign:"left", marginBottom:16, maxHeight:200, overflowY:"auto", whiteSpace:"pre-wrap", wordBreak:"break-all" }}>
                    {this.state.stack}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize:13, color:"#5a6a5a", lineHeight:1.55, marginBottom:20 }}>
                Sorry — something on our end broke. Our team has been notified automatically.
                Try reloading; if it keeps happening, contact your Clover Ag agent at{" "}
                <a href="mailto:office@cloverag.com" style={{ color:"#2d6a2d" }}>office@cloverag.com</a> or (585) 233-1066.
              </div>
            )}
            <button onClick={() => window.location.reload()} style={{ padding:"10px 24px", background:"#2d4a2d", color:"#fff", border:"none", borderRadius:7, fontWeight:800, fontSize:13, cursor:"pointer" }}>
              Reload App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Desktop mobile-preview device frame + toggle. Admin-only (Kevin/Mike). When
// active on a wide screen, the whole app renders inside a 390px-wide, full-height
// phone frame; the frame's `transform` makes the app's position:fixed overlays
// (field detail, modals, bottom nav) resolve against the frame, not the desktop
// viewport, so the preview is faithful. On an actual phone (narrow window) there's
// nothing to frame — it just passes through. Pairs with portal preview: an admin
// can preview another user AND view it as mobile at the same time.
function MobilePreviewFrame({ children }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTick(t => t + 1);   // re-derive on toggle / resize / login
    window.addEventListener("cloverag:forcemobile", h);
    window.addEventListener("resize", h);
    window.addEventListener("cloverag:auth", h);
    return () => {
      window.removeEventListener("cloverag:forcemobile", h);
      window.removeEventListener("resize", h);
      window.removeEventListener("cloverag:auth", h);
    };
  }, []);
  const on        = getForceMobile();
  const wide      = window.innerWidth >= 768;
  const canToggle = isCurrentUserAdmin();     // QA tool — admins only
  const framed    = on && wide && canToggle;  // nothing to frame on a real phone

  return (
    <>
      {framed ? (
        <div style={{ position:"fixed", inset:0, zIndex:2147483000, background:"#141b14", display:"flex", justifyContent:"center" }}>
          <div style={{ position:"absolute", top:10, left:14, color:"#8ad078", fontSize:11, fontWeight:800,
            letterSpacing:"0.08em", textTransform:"uppercase", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>📱 Mobile preview · 390 px</div>
          {/* Full viewport HEIGHT (== the app shell's 100dvh) so the bottom nav lands
              exactly; the bezel is a box-shadow spread (cosmetic, no layout height) so
              it doesn't clip the app. transform:translateZ(0) makes the app's
              position:fixed overlays resolve against this frame, not the desktop. */}
          <div style={{ position:"relative", width:390, maxWidth:"100vw", height:"100%",
            overflow:"hidden", background:"#f2f6f2", boxShadow:"0 0 0 6px #05090a, 0 20px 70px rgba(0,0,0,0.55)",
            transform:"translateZ(0)" }}>
            {children}
          </div>
        </div>
      ) : children}
      {/* Desktop only — on a real phone there's nothing to preview, and the FAB
          overlapped the bottom-nav menu button. (framed also requires wide.) */}
      {canToggle && wide && (
        <button onClick={() => setForceMobile(!getForceMobile())}
          title={on ? "Exit mobile preview" : "Preview the mobile layout on desktop"}
          // Off: a small faded icon, so the admin-only QA toggle stops sitting on
          // top of table columns. Full label + opacity on hover, and always while on.
          onMouseEnter={ev => { if (!on) ev.currentTarget.style.opacity = '1'; }}
          onMouseLeave={ev => { if (!on) ev.currentTarget.style.opacity = '0.45'; }}
          style={{ position:"fixed", right:12, bottom:12, zIndex:2147483001, opacity: on ? 1 : 0.45,
            background: on ? "#8ad078" : "#1a2e1a", color: on ? "#0d140d" : "#8ad078",
            border:"none", borderRadius:18, padding: on ? "8px 13px" : "7px 9px", fontSize:13, fontWeight:800, cursor:"pointer",
            boxShadow:"0 3px 10px rgba(0,0,0,0.25)", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
          {on ? "🖥 Exit mobile" : "📱"}
        </button>
      )}
    </>
  );
}

function AppWithBoundary() {
  return <ErrorBoundary><MobilePreviewFrame><App /></MobilePreviewFrame></ErrorBoundary>;
}

// ═══════════════════════════════════════════════════════════════════════════
// GRAIN CASH TARGETS — the working-order book, and the form that writes it
//
// Kevin, 2026-08-24: "build it, make it easy to fill in, either drop downs or
// type to search. Then make print easy, and then build a nice display page for
// all the working targets. Note targets that have expired. Be able to sort/
// filter by date entered, contract board month, price, everything."
//
// THE DROPDOWNS ARE DRIVEN BY THE LIVE BOARD, and that is the whole point.
// Five of the ten paper tickets recorded no delivery point, worth up to 34c
// ($3,740 on one ticket), and one paired an April shipment with a K27 board
// the elevator prices off H27. Neither is possible here: the point list is the
// points that actually bid this commodity today, the window list is the windows
// that point actually quotes, and the CME month and basis come from the chosen
// board row rather than from memory.
//
// The futures target is COMPUTED (`cash - basis`) and never typed, so the
// ticket balances by construction — which is also what the CHECK constraint on
// grain_cash_targets enforces at the database.
// ═══════════════════════════════════════════════════════════════════════════

const GT_LANE_STYLE = {
  burn:         { bg: "#fdecea", fg: "#a3261a", label: "Raise target" },
  miss:         { bg: "#eef4fd", fg: "#1c4e8a", label: "Lower target" },
  unchanged:    { bg: "#eef7ee", fg: "#2d6a2d", label: "Current" },
  basis_locked: { bg: "#f3f0fa", fg: "#5a3d8a", label: "Basis locked" },
  unresolved:   { bg: "#fdf5e6", fg: "#8a5a00", label: "No board price" },
  ticket_error: { bg: "#fdf5e6", fg: "#8a5a00", label: "Does not balance" },
  unsupported:  { bg: "#f2f2f2", fg: "#555", label: "Not a cash target" },
  closed:       { bg: "#f2f2f2", fg: "#777", label: "Closed" },
};
const gtMoney = (n) => (n == null || !Number.isFinite(Number(n)) ? "—" : "$" + Number(n).toFixed(2));
const gtCents = (n) => (n == null || !Number.isFinite(Number(n)) ? "—"
  : (Number(n) >= 0 ? "+" : "−") + Math.round(Math.abs(Number(n)) * 100) + "¢");
const gtBu = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
// ── NAME THE ALTERNATIVES; NEVER GUESS THE NUMBER ──────────────────────
// "window not on current sheet" is accurate and useless on its own: it sends
// you to the paper sheet to find out what the elevator prints now. The
// resolver has already computed exactly that list and it was being dropped at
// the render.
//
// It stays a LIST and never becomes a match. The elevator collapses and splits
// its shipment windows routinely — Aug-Sep, Oct-Nov and Apr-May recur across a
// year of sheets — so it is tempting to resolve a single month out of the
// combined window that contains it. MEASURED 2026-08-25, that is wrong: the
// 2025-11-13 sheet prints wheat Z25 pickup as BOTH `25-Nov` −15¢ AND `Oct-Nov`
// −55¢ — same commodity, same column, same contract, FORTY CENTS apart (that
// Oct-Nov ties to HARVEST and 26-Sep, i.e. it is the new-crop row). A
// containment rule would silently pick one of two real numbers. So the engine
// still refuses, and the screen says what there is to pick from.
//
// Same contract first — that is the row you are looking for. Capped, with the
// remainder COUNTED rather than silently dropped.
const gtAvailableWindows = (r) => {
  const all = r && r.resolution && r.resolution.available;
  if (!Array.isArray(all) || !all.length) return "";
  const want = String((r && r.futuresContract) || "").trim().toUpperCase();
  const same = want ? all.filter((a) => String(a).toUpperCase().includes(want)) : [];
  const rest = all.filter((a) => !same.includes(a));
  const ordered = [...same, ...rest];
  const shown = ordered.slice(0, 6);
  return shown.join(" · ") + (ordered.length > shown.length
    ? " · +" + (ordered.length - shown.length) + " more" : "");
};
// ── WHAT THE BOARD HAS DONE TO A RESTING ORDER, IN ONE LINE ────────
//
// The book's own columns are all basis mechanics — cash target, basis now,
// futures target, move — so two orders in completely different positions read
// identically. Measured 2026-10-06 on the two live working targets: LEFEBER's
// CZ26 $5.50 came within FOUR CENTS on 2026-09-01 and the board is now 53c
// away; Cripple Creek's WN27 $8.40 was never closer than 76c and is now 127c
// away. One was all but done, the other never near, and the page said the same
// thing about both.
//
// It states history and nothing else. No verdict on whether a target is well
// placed, no suggestion to move it, and never a time estimate — the multiple of
// the median daily move is a SCALE so that 53c on corn and 53c on beans are
// comparable, not a count of days until anything happens.
const gtApproachLine = (a) => {
  if (!a || !a.ok) return null;
  const cs = (x) => Math.round(Math.abs(Number(x)) * 100) + "\u00a2";
  const md = (d) => String(d || "").slice(5).replace("-", "/");
  const scale = a.gapMoves != null && Number.isFinite(a.gapMoves)
    ? Math.abs(a.gapMoves).toFixed(1) + "\u00d7 this contract's median daily move" : null;
  // Provenance on every line: which board, the window, and how many settles.
  const prov = a.symbol + " settles since " + a.since + " (n=" + a.n + ")";
  // The board is AT or THROUGH the order. Never "0c away" — a state of its own.
  if (a.aboveMarket) return { text: "board " + gtMoney(a.lastClose) + " is at or through this",
    title: prov + ". Latest settle " + gtMoney(a.lastClose) + " on " + a.lastDate
      + ", at or above the " + gtMoney(a.level) + " this order rests at." };
  if (a.reached) return { text: "board settled " + gtMoney(a.best.close) + " " + md(a.best.onDate),
    title: prov + ". Highest settle " + gtMoney(a.best.close) + " on " + a.best.onDate
      + ", at or above " + gtMoney(a.level) + ". A settlement is not a fill." };
  if (a.bestIsNow) return { text: cs(a.shortBy) + " short — closest it has been",
    title: prov + ". The latest settle " + gtMoney(a.lastClose) + " on " + a.lastDate
      + " is also the closest this board has come while the order rested: "
      + cs(a.shortBy) + " short of " + gtMoney(a.level) + "."
      + (scale ? " That gap is " + scale + "." : "") };
  return {
    text: cs(a.shortBy) + " short " + md(a.best.onDate) + " · " + cs(a.gapNow) + " now",
    title: prov + ". Closest settle " + gtMoney(a.best.close) + " on " + a.best.onDate
      + ", " + cs(a.shortBy) + " short of " + gtMoney(a.level) + ". Latest settle "
      + gtMoney(a.lastClose) + " on " + a.lastDate + ", " + cs(a.gapNow) + " away — "
      + cs(a.offBest) + " further off than at its closest."
      + (scale ? " That gap is " + scale + "." : "") };
};
const GT_TODAY = () => new Date().toISOString().slice(0, 10);

// `canDelete` matches sql/049 exactly: a staff member granted the cash-target
// book gets select/insert/update and NOT delete, because "enter, edit, close"
// is what the grant is for and closing a ticket is an UPDATE of `status`.
// Destroying the record of a grower's order stays with an admin. The buttons
// are hidden rather than left to fail, so nobody is offered a control the
// database will refuse.
function GrainTargetsView({ farmers = [], isMobile, canDelete = true }) {
  const [board, setBoard] = useState([]);
  const [rawTargets, setRawTargets] = useState([]);
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("book");
  const [editing, setEditing] = useState(null);
  // Bumped whenever a BLANK form is wanted. It feeds the form key, so a new
  // target always gets a freshly mounted component rather than whatever the
  // last one was left holding. Belt and braces: clearing  alone would
  // rely on the conditional render unmounting the form, which is a property of
  // the JSX rather than something asserted anywhere.
  const [formNonce, setFormNonce] = useState(0);
  const startNewTarget = () => { setEditing(null); setFormNonce((n) => n + 1); setTab("new"); };
  const [sort, setSort] = useState({ key: "adjustment", dir: "desc" });
  const [filt, setFilt] = useState({ q: "", lane: "", commodity: "", deliveryPoint: "",
    futuresContract: "", expired: false, needsChange: false, enteredFrom: "", enteredTo: "" });
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const knownBuyers = useMemo(() =>
    [...new Set(rawTargets.map((r) => r.buyer).filter(Boolean))].sort(), [rawTargets]);

  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [b, t] = await Promise.all([
        sbFetch("grain_basis_board", { select: "*", fresh: true }),
        sbFetch("grain_cash_targets", { select: "*", order: "entered_on.desc,id.asc", fresh: true }),
      ]);
      setBoard(b || []); setRawTargets(t || []);
      // ⚠ EVERY TARGET'S BOARD, NOT JUST THE WORKING ONES.
      //
      // This was scoped to `status === "working"` and that was right for its only
      // consumer: boardClosedThrough is short-circuited on the closed lane, so a
      // closed row's bars were never read. targetApproach reads them too, and it
      // IS computed for closed rows — the history is still a fact about one, and
      // it is the only thing that surfaces a filled target the board never
      // settled through.
      //
      // Left narrow, the closed book printed the line on its CZ26 and WN27 rows
      // and nothing at all on the SX26 / WU26 / WU27 / CK27 / CN27 ones — a blank
      // cell that could not say why, which is the loose end this book's own
      // "name what you cannot answer" convention exists to prevent. Measured
      // 2026-10-06: 2 symbols / 58 bars widens to 7 / 188.
      //
      // `since` was already the earliest resting date across ALL targets, so only
      // the symbol set was narrow.
      const syms = [...new Set((t || [])
        .map((r) => boardSymbolFor(r.commodity, r.futures_contract)).filter(Boolean))];
      const since = (t || []).map((r) => restingSince(r)).filter(Boolean).sort()[0];
      if (syms.length && since) {
        try {
          setBars(await sbFetch("futures_contract_history", {
            select: "contract_sym,bar_date,close",
            filters: ["contract_sym=in.(" + syms.join(",") + ")", "bar_date=gte." + since],
            order: "bar_date.asc,id.asc", fresh: true,
          }));
        } catch { setBars([]); }
      } else setBars([]);
      // An empty book opens on the FORM. There is nothing to look at otherwise,
      // and the first thing anyone does with an empty order book is write one.
      if (!(t || []).length) setTab("new");
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // The board is the ONLY source of current basis. basis_sheet_observations is
  // the photo archive and stops at the last import — reading it here is the
  // 45-day-stale bug this whole tool exists to prevent.
  const { observations, unmappedLocations } = useMemo(
    () => boardToObservations(board, { asOf: board[0]?.sheet_updated || board[0]?.updated_at }),
    [board]);
  const sheetVersion = board[0]?.sheet_version || null;
  const sheetUpdated = board[0]?.sheet_updated || null;

  // Every working target, re-derived against the board, then given a lifecycle.
  const rows = useMemo(() => {
    const asOf = GT_TODAY();
    const review = buildTargetReview(rawTargets.map((r) => ({ ...r, id: r.id })), observations);
    const all = [...review.needsChange, ...review.unchanged, ...review.basisLocked,
                 ...review.needsAttention, ...review.closed];
    const bySym = new Map();
    for (const b of bars) {
      if (!bySym.has(b.contract_sym)) bySym.set(b.contract_sym, []);
      bySym.get(b.contract_sym).push(b);
    }
    return all.map((r) => {
      const life = targetLifecycle(r, asOf);
      const sym = boardSymbolFor(r.commodity, r.futuresContract);
      // Narrowed to ONE contract before comparing — two delivery years trade at
      // once, and a max across them is not a price this order could be hit at.
      const cross = r.lane === "closed" ? { crossed: false }
        : boardClosedThrough(r, bySym.get(sym) || []);
      // ⚠ THE SAME NARROWED BARS AND THE SAME RESTING WINDOW as the crossing
      // test above, because targetApproach derives its window from the same
      // restingSince() rule. If the two ever disagreed a row could read
      // "crossed" in one column and "4c short" in the next.
      //
      // Computed for the CLOSED lane too, unlike `cross`, which is short-
      // circuited there because a human-closed row can never be "crossed".
      // The history is still a fact about a closed row, and it is the only
      // thing that surfaces the one filled target the board never settled
      // through (Edmund Acres WN27 $7.80, closest 16c short).
      const approach = targetApproach(r, bySym.get(sym) || []);
      return { ...r, ...life, cross, approach, farmerId: r.farmer_id ?? null,
               enteredOn: r.enteredOn || r.entered_on || "" };
    });
  }, [rawTargets, observations, bars]);

  // Two books. A target the board has settled through is not necessarily filled
  // — the feed is settle-only — so it leaves the working list and goes to a page
  // that shows the evidence and asks.
  // Off the book entirely — a human said filled or cancelled. Checked FIRST,
  // because such a row can never be "crossed" (see the lane guard above) and
  // must not fall through into the working list by default.
  const closedRows = useMemo(() => rows.filter((r) => r.lane === "closed"), [rows]);
  const crossedRows = useMemo(
    () => rows.filter((r) => r.lane !== "closed" && r.cross && r.cross.crossed), [rows]);
  const workingRows = useMemo(
    () => rows.filter((r) => r.lane !== "closed" && !(r.cross && r.cross.crossed)), [rows]);

  const facets = useMemo(() => filterFacets(workingRows), [workingRows]);
  const shown = useMemo(() => {
    const f = { ...filt };
    Object.keys(f).forEach((k) => { if (f[k] === "" ) delete f[k]; });
    if (filt.expired !== true) delete f.expired;         // false means "no filter"
    if (filt.needsChange !== true) delete f.needsChange;
    return sortTargets(filterTargets(workingRows, f), sort.key, sort.dir);
  }, [workingRows, filt, sort]);

  const totals = useMemo(() => {
    const live = workingRows.filter((r) => !r.expired && r.lane !== "closed");
    return {
      working: live.length,
      needsChange: live.filter((r) => r.lane === "burn" || r.lane === "miss").length,
      expired: workingRows.filter((r) => r.expired && r.lane !== "closed").length,
      bushels: live.reduce((s, r) => s + (Number(r.bushels) || 0), 0),
    };
  }, [workingRows]);

  const setSortKey = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });

  // ── printing ────────────────────────────────────────────────────────────
  // One sheet per target, straight through the renderer the print checker
  // guards. Opening a window and calling print() is the pattern the LRP
  // monthly summary already uses — no new dependency.
  const printSheets = (list) => {
    const tickets = list.map((r) => ({
      buyer: r.buyer, enteredOn: r.enteredOn, farmerName: r.farmerName,
      commodity: r.commodity, structure: r.orderType === "hta" ? "HTA"
        : r.orderType === "basis_contract" ? "Basis" : "Cash Target",
      bushels: r.bushels, deliveryWindow: r.deliveryWindow, futuresContract: r.futuresContract,
      deliveryPoint: r.deliveryPoint, bidStructure: r.bidStructure, notes: r.notes,
      // The basis the order was ANCHORED at. The board's current basis still
      // wins where there is one (renderTargetSheet reads opts.basis first), so
      // a working sheet is unchanged; this is what a closed one falls back to
      // instead of printing an empty field.
      basisAtEntry: r.basisAtEntry,
      rungs: [{ futuresTarget: r.requiredFutures ?? r.futuresTargetEntered,
                cashTarget: r.cashTarget, bushels: r.bushels }],
      provenance: r.currentBasis == null ? {} : {
        basis: r.currentBasis, basisSource: `${r.deliveryPoint} ${r.bidStructure}`,
        sheetVersion, sheetDate: sheetUpdated },
    }));
    const w = window.open("", "_blank");
    if (!w) { setFlash({ bad: true, msg: "Pop-up blocked — allow pop-ups to print." }); return; }
    writePrintWindow(w, renderTargetSheetDocument(tickets));
    setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 350);
  };

  // The page computes the SAME review the email does; the endpoint recomputes
  // server-side rather than trusting a posted list, so a stale tab cannot mail
  // yesterday's numbers to the office.
  const sendAlertNow = async () => {
    try {
      // force=1: a human pressing the button always sends. The CRON omits it and
      // so only fires when the board has changed since the last alert.
      const res = await authedFetch(`/api/market?fn=cash-target-alert&force=1`, { method: "POST" });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setFlash({ msg: j.sent ? `Alert emailed to ${j.recipients} recipient(s) — ${j.needsChange} target(s).`
                              : `Nothing to send: ${j.reason || "no targets need changing"}.` });
    } catch (e) { setFlash({ bad: true, msg: "Alert failed: " + (e.message || String(e)) }); }
  };

  // PATCH, not upsert: an upsert of a partial row has to build a valid INSERT
  // tuple first, and every NOT NULL column would have to be resent.
  const patchTarget = async (id, body) => {
    const res = await authedFetch(`${SUPABASE_URL}/rest/v1/grain_cash_targets?id=eq.${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify({ ...body, updated_at: new Date().toISOString() }),
    });
    if (!res.ok) throw new Error(await res.text());
  };

  // ── MARK UPDATED — Kevin, 2026-08-24: "Instead of mark filled, we need
  // ability to mark updated." This is not a tick-box. He has just moved the
  // resting order at the broker to the new futures target, so the TICKET has
  // to be re-anchored to say so: the futures leg becomes what he entered, and
  // the entry basis becomes TODAY's basis. The cash target never moves — it is
  // the promise — which is also what keeps cash = futures + basis true, the
  // CHECK constraint the table enforces. The row then reads Current, and the
  // next basis move measures from here.
  const markUpdated = async (row) => {
    if (row.requiredFutures == null || row.currentBasis == null) {
      setFlash({ bad: true, msg: "No board price for that target — nothing to re-anchor to." });
      return;
    }
    try {
      await patchTarget(row.id, {
        futures_target_entered: row.requiredFutures,
        basis_at_entry: row.currentBasis,
        // NOT slice(0,10). The board stamps its own date the way the elevator
        // prints it — "9/3/2026 13:56" on board 9-C — and ten characters of
        // that is "9/3/2026 1", which Postgres refuses with 22007. That is
        // why this button did nothing and the page showed a raw error body.
        entry_basis_sheet_date: sheetStampToDate(sheetUpdated),
        entry_sheet_version: sheetVersion,
      });
      setFlash({ msg: `${row.farmerName} re-anchored: resting ${gtMoney(row.requiredFutures)} against basis ${gtCents(row.currentBasis)}. Cash target unchanged at ${gtMoney(row.cashTarget)}.` });
      reload();
    } catch (e) { setFlash({ bad: true, msg: e.message || String(e) }); }
  };

  const confirmFilled = async (row) => {
    try { await patchTarget(row.id, { status: "filled" });
      setFlash({ msg: row.farmerName + " marked filled." }); reload(); }
    catch (e) { setFlash({ bad: true, msg: e.message || String(e) }); }
  };
  // Only settles AFTER this moment count from now on, so a HIGHER close later
  // still re-flags it — which a blanket "ignore this row" would lose.
  const dismissFill = async (row) => {
    try { await patchTarget(row.id, { fill_dismissed_at: new Date().toISOString() });
      setFlash({ msg: row.farmerName + " is back in the working book." }); reload(); }
    catch (e) { setFlash({ bad: true, msg: e.message || String(e) }); }
  };

  const deleteTarget = async (row) => {
    if (!window.confirm("Delete " + row.farmerName + "'s " + row.commodity + " target for " +
        row.deliveryWindow + "? This cannot be undone.")) return;
    try {
      await sbDelete("grain_cash_targets", "id=eq." + row.id);
      setFlash({ msg: row.farmerName + "'s target deleted." });
      reload();
    } catch (e) { setFlash({ bad: true, msg: e.message || String(e) }); }
  };

  const markStatus = async (row, status) => {
    try {
      await patchTarget(row.id, { status });
      setFlash({ msg: `${row.farmerName} marked ${status}.` });
      reload();
    } catch (e) { setFlash({ bad: true, msg: e.message || String(e) }); }
  };

  const S = {
    page: { padding: isMobile ? 12 : 22, fontFamily: "'Plus Jakarta Sans','Helvetica Neue',sans-serif", color: "#1a2e1a" },
    card: { background: "#fff", border: "1px solid #dde5dd", borderRadius: 10, padding: 16, marginBottom: 16 },
    h: { margin: "0 0 4px", fontSize: 20, fontWeight: 800 },
    sub: { margin: 0, color: "#5d6b5d", fontSize: 13 },
    kpi: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 },
    kbox: { background: "#fff", border: "1px solid #dde5dd", borderRadius: 10, padding: "10px 14px", minWidth: 120 },
    // A count you cannot click is a count you then go and re-find by hand.
    kboxOn: (on) => ({ background: on ? "#eef4ee" : "#fff", border: "1px solid " + (on ? "#2d4a2d" : "#dde5dd"),
      borderRadius: 10, padding: "10px 14px", minWidth: 120, cursor: "pointer", textAlign: "left",
      fontFamily: "inherit" }),
    kn: { fontSize: 22, fontWeight: 800 }, kl: { fontSize: 11, color: "#5d6b5d", textTransform: "uppercase", letterSpacing: .4 },
    tab: (on) => ({ padding: "8px 16px", borderRadius: 8, border: "1px solid " + (on ? "#2d4a2d" : "#dde5dd"),
      background: on ? "#2d4a2d" : "#fff", color: on ? "#fff" : "#2d4a2d", cursor: "pointer", fontWeight: 700, fontSize: 13 }),
    th: (key) => ({ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase",
      letterSpacing: .3, color: "#5d6b5d", cursor: key ? "pointer" : "default", whiteSpace: "nowrap",
      padding: "8px 8px",
      borderBottom: "2px solid #dde5dd", userSelect: "none" }),
    td: { padding: "6px 6px", fontSize: 12.5, borderBottom: "1px solid #eef2ee", whiteSpace: "nowrap" },
    inp: { padding: "7px 9px", border: "1px solid #cfd8cf", borderRadius: 7, fontSize: 13, fontFamily: "inherit" },
    lbl: { display: "block", fontSize: 11, fontWeight: 700, color: "#5d6b5d", marginBottom: 3, textTransform: "uppercase", letterSpacing: .3 },
    btn: (primary) => ({ padding: "9px 16px", borderRadius: 8, border: "1px solid " + (primary ? "#2d4a2d" : "#cfd8cf"),
      background: primary ? "#2d4a2d" : "#fff", color: primary ? "#fff" : "#2d4a2d", cursor: "pointer", fontWeight: 700, fontSize: 13 }),
  };

  if (loading) return <div style={S.page}>Loading targets…</div>;
  if (err) return <div style={{ ...S.page, color: "#a3261a" }}>Could not load targets: {err}</div>;

  return (
    <div style={S.page}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ ...uiPageTitle(isMobile), marginBottom: 4 }}>Cash Targets</h2>
          <p style={S.sub}>
            <b>Cash target</b> is what the grower agreed to. <b>Basis now</b> is today's board.
            <b>Futures target = cash target − basis</b>, and that is the number to enter at the elevator —
            <b>Resting now</b> is what is sitting there today.
            {sheetVersion ? <> Board <b>{sheetVersion}</b>{sheetUpdated ? ` · ${sheetUpdated}` : ""}.</> : null}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={S.tab(tab === "book")} onClick={() => setTab("book")}>Working book</button>
          <button style={S.tab(tab === "crossed")} onClick={() => setTab("crossed")}>
            Board closed through{crossedRows.length ? " (" + crossedRows.length + ")" : ""}</button>
          <button style={S.tab(tab === "filled")} onClick={() => setTab("filled")}>
            Filled{closedRows.length ? " (" + closedRows.length + ")" : ""}</button>
          <button style={S.tab(tab === "new")} onClick={startNewTarget}>+ New target</button>
        </div>
      </div>

      {unmappedLocations.length > 0 && (
        <div style={{ ...S.card, background: "#fdf5e6", borderColor: "#e6d3a3" }}>
          <b>{unmappedLocations.length} board column{unmappedLocations.length > 1 ? "s" : ""} not mapped</b> — targets
          delivered there cannot be priced until someone maps {unmappedLocations.join(", ")} in
          basisMarketAliases.js. Nothing was guessed.
        </div>
      )}
      {flash && (
        <div style={{ ...S.card, background: flash.bad ? "#fdecea" : "#eef7ee",
                      borderColor: flash.bad ? "#e8b4ae" : "#c3ddc3" }}
             onClick={() => setFlash(null)}>{flash.msg}</div>
      )}

      {totals.needsChange > 0 && (
        <div style={{ ...S.card, background: "#fdecea", borderColor: "#e8b4ae", borderLeft: "5px solid #a3261a" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#a3261a" }}>
                {totals.needsChange} target{totals.needsChange === 1 ? "" : "s"} no longer
                {totals.needsChange === 1 ? " holds its " : " hold their "}cash price
              </div>
              <div style={{ fontSize: 13, color: "#5d3330", marginTop: 3 }}>
                Basis has moved since these were entered, so the futures order no longer produces
                the price the grower agreed to.
                {sheetVersion ? <> Priced against board <b>{sheetVersion}</b>.</> : null}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={S.btn(false)} onClick={() => { setTab("book");
                setFilt((x) => ({ ...x, needsChange: true, expired: false, lane: "" })); }}>Show them</button>
              <button style={S.btn(true)} onClick={sendAlertNow}>✉ Email the alert</button>
            </div>
          </div>
        </div>)}

      {tab === "filled" ? (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 4px", fontSize: 17 }}>Off the working book</h3>
          <p style={{ ...S.sub, marginBottom: 14 }}>
            Targets somebody marked filled or cancelled. They are kept, not deleted — the basis
            and futures they were anchored at are the record of what was agreed and what was
            entered. <b>Reopen</b> puts one back in the working book and it starts being priced
            against the board again.
          </p>
          {closedRows.length === 0 ? (
            <div style={{ color: "#5d6b5d" }}>Nothing has been marked filled.</div>
          ) : (
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr>{["Farmer", "Entered", "Crop", "Deliver to", "Shipment · board", "Bu",
                           "Cash target", "Basis at entry", "Futures entered", "Status", ""].map((h) => (
                <th key={h} style={S.th(null)}>{h}</th>))}</tr></thead>
              <tbody>{closedRows.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...S.td, fontWeight: 700 }}>{r.farmerName}</td>
                  <td style={{ ...S.td, color: "#5d6b5d" }}>{String(r.enteredOn || "").slice(5).replace("-", "/")}</td>
                  <td style={S.td}>{r.commodity}</td>
                  <td style={S.td}>{r.deliveryPoint}{r.bidStructure === "pickup" ? " (P/U)" : ""}</td>
                  <td style={S.td}>{r.deliveryWindow} · <b>{r.futuresContract}</b></td>
                  <td style={{ ...S.td, textAlign: "right" }}>{gtBu(r.bushels)}</td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 700 }}>{gtMoney(r.cashTarget)}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>{gtCents(r.basisAtEntry)}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    {gtMoney(r.futuresTargetEntered)}
                    {(() => { const ln = gtApproachLine(r.approach); return ln ? (
                      <div style={{ fontSize: 10, color: "#8b968b", whiteSpace: "nowrap" }}
                           title={ln.title}>{ln.text}</div>) : null; })()}
                  </td>
                  <td style={S.td}>
                    <span style={{ background: r.status === "filled" ? "#e3f3e0" : "#f2f2f2",
                      color: r.status === "filled" ? "#2d6a2d" : "#666", padding: "3px 7px",
                      borderRadius: 5, fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap" }}>
                      {r.status === "filled" ? "Filled" : "Cancelled"}</span>
                  </td>
                  <td style={S.td}>
                    <button title="Put this target back in the working book"
                      style={{ ...S.btn(false), padding: "4px 10px", fontSize: 12 }}
                      onClick={() => markStatus(r, "working")}>Reopen</button>
                    <button title="Print this sheet" style={{ ...S.btn(false), padding: "4px 8px", fontSize: 12, marginLeft: 6 }}
                      onClick={() => printSheets([r])}>🖨</button>
                    {canDelete && <button title="Delete this target" style={{ ...S.btn(false), padding: "4px 8px", fontSize: 12, marginLeft: 6, color: "#a3261a" }}
                      onClick={() => deleteTarget(r)}>🗑</button>}
                  </td>
                </tr>))}</tbody>
            </table>)}
        </div>
      ) : tab === "crossed" ? (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 4px", fontSize: 17 }}>The board has settled at or above these</h3>
          <p style={{ ...S.sub, marginBottom: 14 }}>
            <b>This is evidence, not a fill.</b> Our futures feed carries a daily settle only — no
            high, no low, no tick — so a rally that settled back leaves no trace here, and a settle
            at the level says the board closed there, not that your order was hit. Check them and
            say which.
          </p>
          {crossedRows.length === 0 ? (
            <div style={{ color: "#5d6b5d" }}>Nothing has settled through a working target.</div>
          ) : (
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr>{["Farmer", "Crop", "Shipment · board", "Bu", "Cash target",
                           "Resting", "Settled at", "On", ""].map((h) => (
                <th key={h} style={S.th(null)}>{h}</th>))}</tr></thead>
              <tbody>{crossedRows.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...S.td, fontWeight: 700 }}>{r.farmerName}</td>
                  <td style={S.td}>{r.commodity}</td>
                  <td style={S.td}>{r.deliveryWindow} · <b>{r.futuresContract}</b></td>
                  <td style={{ ...S.td, textAlign: "right" }}>{gtBu(r.bushels)}</td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 700 }}>{gtMoney(r.cashTarget)}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    {gtMoney(r.futuresTargetEntered)}
                    {(() => { const ln = gtApproachLine(r.approach); return ln ? (
                      <div style={{ fontSize: 10, color: "#8b968b", whiteSpace: "nowrap" }}
                           title={ln.title}>{ln.text}</div>) : null; })()}
                  </td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 800 }}>{gtMoney(r.cross.close)}</td>
                  <td style={S.td}>{r.cross.onDate}</td>
                  <td style={S.td}>
                    <button style={{ ...S.btn(true), padding: "4px 10px", fontSize: 12 }}
                      onClick={() => confirmFilled(r)}>It filled</button>
                    <button style={{ ...S.btn(false), padding: "4px 10px", fontSize: 12, marginLeft: 6 }}
                      onClick={() => dismissFill(r)}>Did not fill</button>
                  </td>
                </tr>))}</tbody>
            </table>)}
        </div>
      ) : tab === "new"
        ? <GrainTargetForm key={editing ? "edit-" + editing.id : "new-" + formNonce}
            farmers={farmers} board={board} observations={observations}
            knownBuyers={knownBuyers} editing={editing}
            onCancelEdit={() => { setEditing(null); setTab("book"); }}
            sheetVersion={sheetVersion} sheetUpdated={sheetUpdated} S={S} saving={saving}
            setSaving={setSaving}
            onPrint={(ticket) => {
              const w = window.open("", "_blank");
              if (!w) { setFlash({ bad: true, msg: "Pop-up blocked — allow pop-ups to print." }); return; }
              writePrintWindow(w, renderTargetSheetDocument([ticket]));
              setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 350);
            }}
            onSaved={(msg) => { setFlash({ msg }); setEditing(null); setFormNonce((n) => n + 1);
              setTab("book"); reload(); }}
            onError={(msg) => setFlash({ bad: true, msg })} />
        : (
        <>
          <div style={S.kpi}>
            <button style={S.kboxOn(filt.needsChange !== true && filt.expired !== true && !filt.lane)}
              onClick={() => setFilt({ ...filt, needsChange: false, expired: false, lane: "" })}>
              <div style={S.kn}>{totals.working}</div><div style={S.kl}>Working</div></button>
            <button style={S.kboxOn(filt.needsChange === true)}
              onClick={() => setFilt({ ...filt, needsChange: true, expired: false, lane: "" })}>
              <div style={{ ...S.kn, color: totals.needsChange ? "#a3261a" : "#2d6a2d" }}>{totals.needsChange}</div>
              <div style={S.kl}>Need changing</div></button>
            <div style={S.kbox}><div style={S.kn}>{gtBu(totals.bushels)}</div><div style={S.kl}>Bushels working</div></div>
            <button style={S.kboxOn(filt.expired === true)}
              onClick={() => setFilt({ ...filt, expired: true, needsChange: false, lane: "" })}>
              <div style={{ ...S.kn, color: totals.expired ? "#8a5a00" : "#1a2e1a" }}>{totals.expired}</div>
              <div style={S.kl}>Expired</div></button>
          </div>

          <div style={{ ...S.card, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div><label style={S.lbl}>Search</label>
              <input style={{ ...S.inp, width: 190 }} placeholder="farmer, point, month…"
                value={filt.q} onChange={(e) => setFilt({ ...filt, q: e.target.value })} /></div>
            <div><label style={S.lbl}>Status</label>
              <select style={S.inp} value={filt.lane} onChange={(e) => setFilt({ ...filt, lane: e.target.value })}>
                <option value="">All</option>
                {facets.lanes.map((l) => <option key={l} value={l}>{(GT_LANE_STYLE[l] || {}).label || l}</option>)}
              </select></div>
            <div><label style={S.lbl}>Crop</label>
              <select style={S.inp} value={filt.commodity} onChange={(e) => setFilt({ ...filt, commodity: e.target.value })}>
                <option value="">All</option>{facets.commodities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label style={S.lbl}>Delivery point</label>
              <select style={S.inp} value={filt.deliveryPoint} onChange={(e) => setFilt({ ...filt, deliveryPoint: e.target.value })}>
                <option value="">All</option>{facets.deliveryPoints.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></div>
            <div><label style={S.lbl}>Board month</label>
              <select style={S.inp} value={filt.futuresContract} onChange={(e) => setFilt({ ...filt, futuresContract: e.target.value })}>
                <option value="">All</option>{facets.contracts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label style={S.lbl}>Entered from</label>
              <input type="date" style={S.inp} value={filt.enteredFrom} onChange={(e) => setFilt({ ...filt, enteredFrom: e.target.value })} /></div>
            <div><label style={S.lbl}>to</label>
              <input type="date" style={S.inp} value={filt.enteredTo} onChange={(e) => setFilt({ ...filt, enteredTo: e.target.value })} /></div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, paddingBottom: 7 }}>
              <input type="checkbox" checked={filt.expired === true}
                onChange={(e) => setFilt({ ...filt, expired: e.target.checked })} /> Expired only
            </label>
            <button style={S.btn(false)} onClick={() => setFilt({ q: "", lane: "", commodity: "",
              deliveryPoint: "", futuresContract: "", expired: false, needsChange: false,
              enteredFrom: "", enteredTo: "" })}>Clear</button>
            <div style={{ flex: 1 }} />
            <button style={S.btn(false)} disabled={!shown.length}
              onClick={() => printSheets(shown)}>🖨 Print {shown.length} sheet{shown.length === 1 ? "" : "s"}</button>
            {shown.some((r) => r.lane === "burn" || r.lane === "miss") && (
              <button style={S.btn(true)} onClick={async () => {
                const list = shown.filter((r) => r.lane === "burn" || r.lane === "miss");
                for (const r of list) { try { await markUpdated(r); } catch {} }
                setFlash({ msg: `Re-anchored ${list.length} target${list.length === 1 ? "" : "s"} to today's basis.` });
              }}>✓ Mark {shown.filter((r) => r.lane === "burn" || r.lane === "miss").length} updated</button>)}
          </div>

          {/* Setup, not the work: collapsed so the targets table is not pushed
              below an admin settings panel on every visit. */}
          {isCurrentUserAdmin() && <details style={{ margin: "0 0 12px" }}>
            <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#2d4a2d" }}>✉ Alert recipients</summary>
            <div style={{ marginTop: 8 }}><MIRecipientManager
            table="cash_target_alert_recipients"
            title="Cash-target alert recipients"
            blurb="Recipients get an email when a target's futures order no longer produces the cash price the grower agreed to. It fires only when the set of stale targets CHANGES, so the same rows going unfixed send nothing further."
          /></div>
          </details>}

          <div style={{ ...S.card, padding: 0, overflowX: "auto" }}>
            {shown.length === 0 ? (
              <div style={{ padding: 22, color: "#5d6b5d" }}>
                {workingRows.length === 0
                  ? "No targets yet — use “+ New target” to write the first one."
                  : filt.expired === true ? "No expired targets — every board is still trading."
                  : "No targets match these filters."}
              </div>
            ) : (
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1080 }}>
              <thead><tr>
                {[["farmer", "Farmer"], ["entered", "Entered"], ["commodity", "Crop"],
                  ["point", "Deliver to"], ["window", "Shipment · board"],
                  ["bushels", "Bu"],
                  // The three that answer the question, in the order it is
                  // asked: what did the grower agree to, what is basis today,
                  // and therefore what futures price do we enter at.
                  ["cash", "Cash target"], ["basisNow", "Basis now"], ["needs", "Futures target"],
                  ["resting", "Resting now"], ["adjustment", "Move"],
                  ["status", "Status"], [null, ""]].map(([k, lbl]) => (
                  <th key={lbl} style={S.th(k)} onClick={k ? () => setSortKey(k) : undefined}>
                    {lbl}{sort.key === k ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                  </th>))}
              </tr></thead>
              <tbody>
                {shown.map((r) => {
                  const st = GT_LANE_STYLE[r.lane] || GT_LANE_STYLE.unsupported;
                  return (
                  <tr key={r.id} style={{ background: r.expired ? "#fafafa" : undefined,
                                          opacity: r.expired ? .65 : 1 }}>
                    <td style={{ ...S.td, fontWeight: 700, maxWidth: 165, overflow: "hidden",
                                 textOverflow: "ellipsis" }} title={r.farmerName}>
                      {r.farmerName}
                      {r.expired && <span style={{ marginLeft: 7, fontSize: 10, fontWeight: 800,
                        background: "#eee", color: "#666", padding: "2px 6px", borderRadius: 4 }}>EXPIRED</span>}
                      {!r.expired && r.expiringSoon && <span style={{ marginLeft: 7, fontSize: 10, fontWeight: 800,
                        background: "#fdf5e6", color: "#8a5a00", padding: "2px 6px", borderRadius: 4 }}>ROLLS SOON</span>}
                    </td>
                    <td style={{ ...S.td, color: "#5d6b5d" }}>{String(r.enteredOn || "").slice(5).replace("-", "/")}</td>
                    <td style={S.td}>{r.commodity}</td>
                    <td style={S.td}>{r.deliveryPoint}{r.bidStructure === "pickup" ? " (P/U)" : ""}</td>
                    <td style={S.td}>{r.deliveryWindow} · <b>{r.futuresContract}</b></td>
                    <td style={{ ...S.td, textAlign: "right" }}>{gtBu(r.bushels)}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 700 }}>{gtMoney(r.cashTarget)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      {r.currentBasis == null ? "—" : gtCents(r.currentBasis)}
                      {r.basisAtEntry != null && r.currentBasis != null && r.basisAtEntry !== r.currentBasis && (
                        <div style={{ fontSize: 10, color: "#8b968b" }}>was {gtCents(r.basisAtEntry)}</div>)}
                    </td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 800, fontSize: 14,
                       color: r.lane === "burn" ? "#a3261a" : r.lane === "miss" ? "#1c4e8a" : undefined }}>
                      {gtMoney(r.requiredFutures)}</td>
                    <td style={{ ...S.td, textAlign: "right", color: "#5d6b5d" }}>
                      {gtMoney(r.futuresTargetEntered)}
                      {/* Under the resting price, where the eye already is, and in the
                          same sub-line shape the "was +15\u00a2" basis note next to it
                          already uses — so this adds no column to a table that is
                          already 13 wide and scrolls sideways on a phone. */}
                      {(() => { const ln = gtApproachLine(r.approach); return ln ? (
                        <div style={{ fontSize: 10, color: "#8b968b", whiteSpace: "nowrap" }}
                             title={ln.title}>{ln.text}</div>) : null; })()}
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>{r.adjustment == null ? "—" : gtCents(r.adjustment)}</td>

                    <td style={S.td}>
                      <span style={{ background: st.bg, color: st.fg, padding: "3px 7px",
                        borderRadius: 5, fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap" }}>{st.label}</span>
                      {r.reason && (r.lane === "unresolved" || r.lane === "ticket_error") &&
                        <div style={{ fontSize: 10, color: "#8a5a00", marginTop: 3, whiteSpace: "normal",
                                      maxWidth: 190, lineHeight: 1.35 }}>{String(r.reason).replace(/_/g, " ")}</div>}
                      {r.lane === "unresolved" && gtAvailableWindows(r) &&
                        <div style={{ fontSize: 10, color: "#5d6b5d", marginTop: 3, whiteSpace: "normal",
                                      maxWidth: 190, lineHeight: 1.35 }}>
                          Sheet carries: {gtAvailableWindows(r)}</div>}
                    </td>
                    <td style={S.td}>
                      {(r.lane === "burn" || r.lane === "miss") && (
                        <button title={`Mark updated — I have moved the resting order to ${gtMoney(r.requiredFutures)}`}
                          style={{ ...S.btn(false), padding: "3px 7px", fontSize: 12, marginLeft: 4,
                                   borderColor: "#2d4a2d", fontWeight: 800 }}
                          onClick={() => markUpdated(r)}>✓</button>)}
                    </td>
                      <button title="Print this sheet" style={{ ...S.btn(false), padding: "2px 5px", fontSize: 12 }}
                        onClick={() => printSheets([r])}>🖨</button>
                      <button title="Edit this target" style={{ ...S.btn(false), padding: "2px 5px", fontSize: 11, marginLeft: 3 }}
                        onClick={() => { setEditing(r); setTab("new"); }}>✎</button>
                      {canDelete && <button title="Delete this target" style={{ ...S.btn(false), padding: "3px 7px", fontSize: 12,
                               marginLeft: 4, borderColor: "#e8b4ae", color: "#a3261a" }}
                        onClick={() => deleteTarget(r)}>🗑</button>}
                  </tr>);
                })}
              </tbody>
            </table>)}
          </div>
          <p style={{ ...S.sub, marginTop: -6 }}>
            Showing {shown.length} of {workingRows.length}. A target expires when its board month goes into
            delivery — the elevator rolls off the board the month before — or when a dated shipment
            window has passed. Nothing here infers a fill; mark them yourself.
          </p>
        </>)}
    </div>
  );
}

// ── the entry form ────────────────────────────────────────────────────────
// Every selector below is populated FROM THE BOARD, narrowing left to right, so
// a combination the elevator does not quote cannot be written down.
function GrainTargetForm({ farmers, board, observations, sheetVersion, sheetUpdated, S,
                           saving, setSaving, onSaved, onError, onPrint, knownBuyers = [],
                           editing = null, onCancelEdit }) {
  const [f, setF] = useState({
    farmerId: "", farmerName: "",
    // Remembered between tickets and offered as a list of the ones already
    // used. Kevin printed a sheet with this blank — a field you retype every
    // time is a field you leave empty.
    buyer: (() => { try { return localStorage.getItem("gt_buyer") || ""; } catch { return ""; } })(),
    enteredOn: GT_TODAY(),
    commodity: "corn", bidStructure: "delivered", deliveryPoint: "", deliveryWindow: "",
    orderType: "cash_target", basisLocked: false, notes: "",
  });
  const [rungs, setRungs] = useState([{ cashTarget: "", bushels: "" }]);
  const [farmerQ, setFarmerQ] = useState("");
  // Reading a PAPER ticket into this form. The photo never saves anything —
  // it fills the fields it could read and NAMES the ones it could not, and the
  // person presses Save. Same shape as the basis-sheet importer: extract,
  // report, leave the write a reviewed step.
  const ticketFileRef = useRef(null);
  const [reading, setReading] = useState(false);
  const [readStatus, setReadStatus] = useState(null);   // transient, e.g. a retry
  const [readErr, setReadErr] = useState(null);
  const [readResult, setReadResult] = useState(null);   // { gaps, basisCheck, seen }

  // Load a row being edited. Keyed on the row id so re-renders do not stomp
  // what is being typed.
  useEffect(() => {
    if (!editing) return;
    setF({
      farmerId: editing.farmerId || "", farmerName: editing.farmerName || "",
      buyer: editing.buyer || "", enteredOn: editing.enteredOn || GT_TODAY(),
      commodity: editing.commodity || "corn", bidStructure: editing.bidStructure || "delivered",
      deliveryPoint: editing.deliveryPoint || "",
      deliveryWindow: (editing.deliveryWindow || "") + "|" + (editing.futuresContract || ""),
      orderType: editing.orderType || "cash_target", basisLocked: !!editing.basisLocked,
      notes: editing.notes || "",
    });
    setRungs([{ cashTarget: String(editing.cashTarget ?? ""), bushels: String(editing.bushels ?? "") }]);
    setFarmerQ("");
    // Keyed on the row id ONLY: re-running on every field change would stomp
    // whatever is being typed.
  }, [editing && editing.id]);

  const commodities = useMemo(() =>
    [...new Set(board.map((b) => b.commodity).filter(Boolean))].sort(), [board]);
  // The ticket names a TOWN — Avon, Lakeville, Batavia, LeRoy, Geneva — even
  // though the sheet quotes "Avon & Lakeville" as one column. Only towns the
  // board actually bids for this crop are offered. Picked-up bids are not
  // towns ("P/U West", "Dry P/U East of Lima"), so those keep their headers.
  const liveSlugs = useMemo(() => new Set(board
    .filter((b) => b.commodity === f.commodity && b.term === f.bidStructure && b.basis != null)
    .map((b) => slugForMarket(b.location)).filter(Boolean)), [board, f.commodity, f.bidStructure]);
  const points = useMemo(() => {
    if (f.bidStructure === "delivered") {
      return Object.keys(DELIVERY_TOWN_SLUGS)
        .filter((t) => (slugsForTown(t) || []).some((sl) => liveSlugs.has(sl)));
    }
    return [...new Set(board
      .filter((b) => b.commodity === f.commodity && b.term === f.bidStructure && b.basis != null)
      .map((b) => b.location))].sort();
  }, [board, f.commodity, f.bidStructure, liveSlugs]);
  // A row being EDITED may carry a point written before towns existed (a raw
  // column header). Keep it selectable so an edit round-trips unchanged.
  const pointOptions = useMemo(() =>
    (f.deliveryPoint && !points.includes(f.deliveryPoint)) ? [f.deliveryPoint, ...points] : points,
    [points, f.deliveryPoint]);
  // Windows for whichever COLUMN carries this town, deduped by (ship, board):
  // two columns can cover one town and the engine refuses a genuine
  // disagreement, so the picker must not silently offer one of two prices.
  const windows = useMemo(() => {
    const want = new Set(slugsForTown(f.deliveryPoint) ||
      (f.deliveryPoint ? [slugForMarket(f.deliveryPoint)].filter(Boolean) : []));
    const rows = board.filter((b) => b.commodity === f.commodity && b.term === f.bidStructure &&
      b.basis != null && want.has(slugForMarket(b.location)));
    const byCell = new Map();
    for (const b of rows) {
      const k = b.ship_month + "|" + b.cme_contract;
      if (!byCell.has(k)) byCell.set(k, b);
    }
    return [...byCell.values()].sort((a, b) => (a.ship_sort ?? 0) - (b.ship_sort ?? 0));
  }, [board, f.commodity, f.bidStructure, f.deliveryPoint]);
  const chosen = windows.find((w) => `${w.ship_month}|${w.cme_contract}` === f.deliveryWindow) || null;
  const boardBasis = chosen ? Number(chosen.basis) : null;
  // What the CONTRACT locked, typed in cents the way the sheet writes it
  // (+15 / -6). Only used when this is a basis-contract target.
  const [lockedBasisInput, setLockedBasisInput] = useState(
    editing && editing.basisLocked && editing.basisAtEntry != null
      ? String(Math.round(Number(editing.basisAtEntry) * 100)) : "");
  // Shared, and guarded by check-cash-targets. It was inline here and therefore
  // untestable, which is exactly how the unit-guessing bug shipped.
  const lockedBasis = parseBasisCents(lockedBasisInput);
  // THE basis this ticket prices off. A locked contract wins over the board;
  // everything else follows the board.
  const basis = f.basisLocked ? lockedBasis : boardBasis;

  const farmerMatches = useMemo(() => {
    const q = farmerQ.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set();
    const out = [];
    for (const x of farmers || []) {
      const name = String(x.name || "").trim();
      const key = name.toLowerCase();
      if (!key || seen.has(key) || !key.includes(q)) continue;
      seen.add(key);
      out.push({ id: x.id, name });
      if (out.length >= 8) break;
    }
    return out;
  }, [farmers, farmerQ]);

  // cash - basis, in integer hundredths of a cent, the same arithmetic the
  // engine and the CHECK constraint use.
  const futuresFor = (cash) => {
    const c = Number(cash);
    if (!Number.isFinite(c) || basis == null) return null;
    return unq(q(c) - q(basis));
  };

  // What the user MEANT: a picked farmer if they clicked one, otherwise
  // whatever they typed. A grain customer need not be in the insurance book.
  const effectiveFarmer = (f.farmerName || farmerQ).trim();
  // Named individually so the form can say which one is missing rather than
  // reciting all four.
  const missing = [
    !effectiveFarmer && "a farmer name",
    !f.deliveryPoint && "a delivery point",
    f.basisLocked && lockedBasis == null && "the locked basis from the contract",
    !chosen && "a shipment period",
    !rungs.some((r) => Number(r.cashTarget) > 0 && Number(r.bushels) > 0) && "a cash price and bushels",
  ].filter(Boolean);
  const ready = missing.length === 0;

  const asTicket = () => ({
    buyer: f.buyer, enteredOn: f.enteredOn, farmerName: effectiveFarmer, commodity: f.commodity,
    structure: f.orderType === "hta" ? "HTA" : f.orderType === "basis_contract" ? "Basis" : "Cash Target",
    bushels: rungs.reduce((s, r) => s + (Number(r.bushels) || 0), 0),
    deliveryWindow: chosen ? chosen.ship_month : "", futuresContract: chosen ? chosen.cme_contract : "",
    deliveryPoint: f.deliveryPoint, bidStructure: f.bidStructure, truckingNotes: f.notes,
    rungs: rungs.filter((r) => Number(r.cashTarget) > 0).map((r) => ({
      futuresTarget: futuresFor(r.cashTarget), cashTarget: Number(r.cashTarget), bushels: Number(r.bushels) })),
    provenance: basis == null ? {} : (f.basisLocked
      ? { basis, basisSource: `${f.deliveryPoint} — basis locked by contract` }
      : { basis, basisSource: `${f.deliveryPoint} ${f.bidStructure}`,
          sheetVersion, sheetDate: sheetUpdated }),
  });

  // ── read a photographed paper ticket into this form ─────────────────────
  // The delivery point is the field this is really about. Five of the ten
  // working tickets photographed on 2026-08-24 recorded none, and that single
  // field decides the basis — worth up to 34c, $3,740 on one ticket. So an
  // unread point stays EMPTY and `missing` keeps the Save button off, rather
  // than the reader picking the farm's usual elevator.
  const readPointCandidates = useMemo(() => [...new Set([
    ...Object.keys(DELIVERY_TOWN_SLUGS),
    ...board.filter((b) => b.term && b.term !== "delivered").map((b) => b.location).filter(Boolean),
  ])], [board]);

  const onTicketPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Cleared immediately so the SAME photo can be retried — a file input does
    // not fire change again for an identical selection.
    e.target.value = "";
    if (!file) return;
    setReading(true); setReadErr(null); setReadStatus(null); setReadResult(null);
    try {
      const { data: b64, mime: media } = await downscaleImage(file, 1568, 0.85);
      const parsed = await extractPhotoJson({
        b64, media, apiFetch: adminApiFetch, onStatus: setReadStatus,
        prompt: targetTicketPrompt({
          towns: Object.keys(DELIVERY_TOWN_SLUGS),
          commodities: SHEET_COMMODITIES,
          structures: SHEET_STRUCTURES,
        }),
      });
      const read = readTargetTicket(parsed, {
        board, farmers, points: readPointCandidates, today: GT_TODAY(),
      });
      // ONLY WHAT IT READ. A blank never overwrites something already on the
      // form — the buyer is remembered between tickets and the date defaults
      // to today, and a reader that could not see either must not erase them.
      const d = read.draft;
      setF((prev) => ({
        ...prev,
        farmerId: d.farmerId || prev.farmerId,
        farmerName: d.farmerName || (d.farmerQuery ? "" : prev.farmerName),
        buyer: d.buyer || prev.buyer,
        enteredOn: d.enteredOn || prev.enteredOn,
        commodity: d.commodity || prev.commodity,
        bidStructure: d.bidStructure || prev.bidStructure,
        deliveryPoint: d.deliveryPoint || "",
        deliveryWindow: d.deliveryWindow || "",
        orderType: d.orderType || prev.orderType,
        basisLocked: !!d.basisLocked,
        notes: d.notes || prev.notes,
      }));
      if (d.farmerQuery) setFarmerQ(d.farmerQuery);
      if (d.lockedBasisInput) setLockedBasisInput(d.lockedBasisInput);
      setRungs(read.rungs.length ? read.rungs : [{ cashTarget: "", bushels: "" }]);
      setReadResult({ gaps: read.gaps, basisCheck: read.basisCheck, seen: read.seen });
    } catch (ex) {
      setReadErr(ex && ex._tries
        ? `The photo never reached the reader after ${ex._tries} tries — ${ex.message || ex}. The ticket can still be typed in below.`
        : `Could not read that photo — ${(ex && ex.message) || ex}. The ticket can still be typed in below.`);
    } finally { setReading(false); setReadStatus(null); }
  };

  const save = async () => {
    if (saving) return;
    if (!ready) {
      onError("Not saved — still needs " + missing.join(", ") + ".");
      return;
    }
    setSaving(true);
    try {
      // ONE ROW PER RUNG. Kludt's real ticket carries 10,000 bu at $6.25 and
      // 10,000 at $6.50 on one sheet; they are two orders.
      const rowsOut = rungs.filter((r) => Number(r.cashTarget) > 0 && Number(r.bushels) > 0).map((r) => ({
        farmer_id: f.farmerId || null, farmer_name: effectiveFarmer, buyer: f.buyer || null,
        entered_on: f.enteredOn, commodity: f.commodity, bushels: Number(r.bushels),
        delivery_point: f.deliveryPoint, delivery_window: chosen.ship_month,
        futures_contract: chosen.cme_contract, bid_structure: f.bidStructure,
        cash_target: Number(r.cashTarget), basis_at_entry: basis,
        futures_target_entered: futuresFor(r.cashTarget),
        basis_locked: !!f.basisLocked, order_type: f.orderType, status: "working",
        // Same rule as the re-anchor above, and the same defect: creating a
        // target against board 9-C failed on the date, not on anything the
        // user typed. A basis-locked ticket carries no sheet date by design.
        entry_basis_sheet_date: f.basisLocked ? null : sheetStampToDate(sheetUpdated),
        entry_sheet_version: f.basisLocked ? null : sheetVersion, notes: f.notes || null,
      }));
      if (editing) {
        // An EDIT is one row, so it patches in place rather than deleting and
        // re-inserting — the id is what the alert de-dupe signature is built
        // from, and a new id would read as a brand new stale target.
        const res = await authedFetch(`${SUPABASE_URL}/rest/v1/grain_cash_targets?id=eq.${editing.id}`, {
          method: "PATCH", headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
          body: JSON.stringify({ ...rowsOut[0], updated_at: new Date().toISOString() }),
        });
        if (!res.ok) throw new Error(await res.text());
        onSaved(`Updated ${effectiveFarmer}'s target.`);
      } else {
        await sbInsert("grain_cash_targets", rowsOut, { returning: "minimal" });
        onSaved(`Saved ${rowsOut.length} target${rowsOut.length > 1 ? "s" : ""} for ${effectiveFarmer}.`);
      }
    } catch (e) { onError(e.message || String(e)); }
    finally { setSaving(false); }
  };

  return (
    <div style={S.card}>
      {/* ── READ A PAPER TICKET ────────────────────────────────────────────
          Two ways in, one form. `capture="environment"` opens the rear camera
          straight away on a phone, so a ticket can be entered standing at the
          target book; on a desktop the same control is a file picker.
          It PREFILLS and never saves — the gaps it names below are the point. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                    padding: "10px 12px", marginBottom: 14, borderRadius: 9,
                    background: "#f2f6f2", border: "1px solid #dde6dd" }}>
        <input ref={ticketFileRef} type="file" accept="image/*" capture="environment"
               onChange={onTicketPhoto} style={{ display: "none" }} />
        <button type="button" disabled={reading}
          style={{ ...S.btn(true), padding: "8px 14px", fontSize: 13,
                   opacity: reading ? 0.6 : 1, cursor: reading ? "wait" : "pointer" }}
          onClick={() => ticketFileRef.current && ticketFileRef.current.click()}>
          {reading ? "Reading the ticket…" : "📷 Photograph a paper ticket"}
        </button>
        <span style={{ fontSize: 12, color: "#5d6b5d" }}>
          {reading
            ? (readStatus || "Reading what is written on it…")
            : "It fills in what it can read and says what it could not. Nothing is saved until you press Save."}
        </span>
      </div>

      {readErr && (
        <div style={{ padding: "10px 12px", marginBottom: 14, borderRadius: 9, fontSize: 12.5,
                      background: "#fdf0f0", border: "1px solid #e6c3c3", color: "#8a2a2a" }}>
          {readErr}
        </div>)}

      {readResult && (
        <div style={{ padding: "10px 12px", marginBottom: 14, borderRadius: 9, fontSize: 12.5,
                      background: readResult.gaps.length ? "#fdf5e6" : "#eef6ea",
                      border: "1px solid " + (readResult.gaps.length ? "#e6d3a3" : "#cfe3c6") }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
            {/* "could not fill in" undersold the case that matters most: a
                transposed price IS filled in, after the arithmetic corrected it.
                "to check" covers a gap and a correction without mislabelling
                either. */}
            <b>{readResult.gaps.length
              ? `Read the ticket — ${readResult.gaps.length} thing${readResult.gaps.length > 1 ? "s" : ""} to check`
              : "Read the ticket — everything on it came through"}</b>
            <button type="button" style={{ background: "none", border: "none", cursor: "pointer",
                                           color: "#60735f", fontSize: 13 }}
              title="Dismiss this note" onClick={() => setReadResult(null)}>✕</button>
          </div>
          {readResult.gaps.length > 0 && (
            <ul style={{ margin: "7px 0 0", paddingLeft: 18, lineHeight: 1.5 }}>
              {readResult.gaps.map((g, i) => <li key={i}>{g}</li>)}
            </ul>)}
          {/* THE DRIFT, SAID AT ENTRY TIME. A ticket written in March against
              +15 is a correct record of March — it is not an error and is not
              a gap. But the futures order this form derives comes off TODAY's
              board, so it will not be the number written on the paper, and
              saying so here is this whole tool applied one step earlier. */}
          {readResult.basisCheck && readResult.basisCheck.moved && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(0,0,0,.08)" }}>
              The ticket was written against basis <b>{gtCents(readResult.basisCheck.writtenBasis)}</b>;
              the board is <b>{gtCents(readResult.basisCheck.boardBasis)}</b> today
              ({gtCents(readResult.basisCheck.drift)} move). The futures target below is derived from
              today's basis, so it will not match the futures price written on the paper — that is the
              point, not a mistake.
            </div>)}
          {readResult.basisCheck && !readResult.basisCheck.moved && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(0,0,0,.08)" }}>
              Basis on the ticket still matches the board at {gtCents(readResult.basisCheck.boardBasis)}.
            </div>)}
        </div>)}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
        <div style={{ position: "relative" }}>
          <label style={S.lbl}>Farmer (type to search)</label>
          <input style={{ ...S.inp, width: "100%" }} value={f.farmerName || farmerQ}
            placeholder="start typing a name…"
            onChange={(e) => { setFarmerQ(e.target.value); setF({ ...f, farmerName: "", farmerId: "" }); }} />
          {farmerMatches.length > 0 && !f.farmerName && farmerQ.trim().length > 1 && (
            <div style={{ position: "absolute", zIndex: 20, background: "#fff", border: "1px solid #cfd8cf",
                          borderRadius: 7, width: "100%", maxHeight: 220, overflowY: "auto", boxShadow: "0 6px 18px rgba(0,0,0,.10)" }}>
              {farmerMatches.map((m) => (
                <div key={m.id} style={{ padding: "7px 10px", cursor: "pointer", fontSize: 13 }}
                  onMouseDown={() => { setF({ ...f, farmerName: m.name, farmerId: m.id }); setFarmerQ(""); }}>
                  {m.name}</div>))}
            </div>)}
          {/* A name the book does not carry is still writable — the ticket is
              written before anyone checks the roster. It saves unlinked. */}
          {/* Offered WHENEVER something is typed, not only on zero matches: a
              grain customer need not be an insurance customer, and with matches
              on screen the escape used to be hidden exactly when it was needed. */}
          {!f.farmerName && farmerQ.trim() && (
            <div style={{ fontSize: 11, marginTop: 4 }}>
              <button style={{ ...S.btn(false), padding: "3px 8px", fontSize: 11 }}
                onMouseDown={() => setF({ ...f, farmerName: farmerQ.trim(), farmerId: "" })}>
                Use “{farmerQ.trim()}” — not an insurance customer</button></div>)}
        </div>

        <div><label style={S.lbl}>Buyer</label>
          <input style={{ ...S.inp, width: "100%" }} value={f.buyer} placeholder="KK"
            list="gt-buyers" onChange={(e) => { setF({ ...f, buyer: e.target.value });
              try { localStorage.setItem("gt_buyer", e.target.value); } catch {} }} />
          <datalist id="gt-buyers">{knownBuyers.map((b) => <option key={b} value={b} />)}</datalist></div>
        <div><label style={S.lbl}>Date</label>
          <input type="date" style={{ ...S.inp, width: "100%" }} value={f.enteredOn}
            onChange={(e) => setF({ ...f, enteredOn: e.target.value })} /></div>

        <div><label style={S.lbl}>Structure</label>
          <select style={{ ...S.inp, width: "100%" }} value={f.orderType}
            onChange={(e) => setF({ ...f, orderType: e.target.value })}>
            <option value="cash_target">Cash Target</option>
            <option value="basis_contract">Basis</option>
            <option value="hta">HTA</option>
          </select></div>

        <div><label style={S.lbl}>Crop</label>
          <select style={{ ...S.inp, width: "100%" }} value={f.commodity}
            onChange={(e) => setF({ ...f, commodity: e.target.value, deliveryPoint: "", deliveryWindow: "" })}>
            {commodities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>

        <div><label style={S.lbl}>Terms</label>
          <select style={{ ...S.inp, width: "100%" }} value={f.bidStructure}
            onChange={(e) => setF({ ...f, bidStructure: e.target.value, deliveryPoint: "", deliveryWindow: "" })}>
            <option value="delivered">Delivered</option>
            <option value="pickup">Pickup / FOB farm</option>
          </select></div>

        <div><label style={S.lbl}>Deliver to <span style={{ color: "#a3261a" }}>*</span></label>
          <select style={{ ...S.inp, width: "100%" }} value={f.deliveryPoint}
            onChange={(e) => setF({ ...f, deliveryPoint: e.target.value, deliveryWindow: "" })}>
            <option value="">— choose —</option>
            {pointOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select></div>

        <div><label style={S.lbl}>Shipment period <span style={{ color: "#a3261a" }}>*</span></label>
          <select style={{ ...S.inp, width: "100%" }} value={f.deliveryWindow} disabled={!f.deliveryPoint}
            onChange={(e) => setF({ ...f, deliveryWindow: e.target.value })}>
            <option value="">{f.deliveryPoint ? "— choose —" : "pick a delivery point first"}</option>
            {windows.map((w) => (
              <option key={`${w.ship_month}|${w.cme_contract}`} value={`${w.ship_month}|${w.cme_contract}`}>
                {w.ship_month} · {w.cme_contract} · {gtCents(Number(w.basis))}</option>))}
          </select></div>

        <div><label style={S.lbl}>CME month (from the board)</label>
          <input style={{ ...S.inp, width: "100%", background: "#f6f8f6" }} readOnly
            value={chosen ? chosen.cme_contract : ""} /></div>

        <div><label style={S.lbl}>
            {f.basisLocked ? "Basis (locked by contract)" : "Basis (from the board)"}
            {f.basisLocked && <span style={{ color: "#a3261a" }}> *</span>}</label>
          {f.basisLocked ? (
            <input style={{ ...S.inp, width: "100%", fontWeight: 700 }} inputMode="text"
              placeholder={boardBasis == null ? "+15" : gtCents(boardBasis) + " on the board"}
              value={lockedBasisInput}
              onChange={(e) => setLockedBasisInput(e.target.value)} />
          ) : (
            <input style={{ ...S.inp, width: "100%", background: "#f6f8f6", fontWeight: 700 }} readOnly
              value={basis == null ? "" : gtCents(basis)} />
          )}
          {f.basisLocked && (
            <div style={{ fontSize: 10, marginTop: 3,
                          color: lockedBasisInput.trim() && lockedBasis == null ? "#a3261a" : "#5d6b5d" }}>
              {lockedBasisInput.trim() && lockedBasis == null
                ? <b>Whole cents only — “+15”, “-6”, “+2”. A decimal is ambiguous, so it is not guessed.</b>
                : <>In cents, as the sheet writes it — <b>+15</b> or <b>-6</b>.</>}
              {" "}The board
              {boardBasis == null ? " has no bid for this window" : " says " + gtCents(boardBasis)},
              and does not apply to a locked contract.
            </div>)}</div>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, margin: "14px 0 4px" }}>
        <input type="checkbox" checked={f.basisLocked}
          onChange={(e) => {
            const on = e.target.checked;
            if (on && !lockedBasisInput && boardBasis != null) {
              setLockedBasisInput(String(Math.round(boardBasis * 100)));
            }
            setF({ ...f, basisLocked: on });
          }} />
        <span><b>This is a target on a basis contract</b> — the basis is already locked, so
          the board cannot move it and it will never be reported as stale.</span>
      </label>

      <div style={{ marginTop: 14, borderTop: "1px solid #eef2ee", paddingTop: 12 }}>
        <div style={{ ...S.lbl, marginBottom: 8 }}>Targets — the futures order is derived, never typed</div>
        {rungs.map((r, i) => {
          const fut = futuresFor(r.cashTarget);
          return (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 8, flexWrap: "wrap" }}>
            <div><label style={S.lbl}>Cash price</label>
              <input style={{ ...S.inp, width: 110 }} inputMode="decimal" placeholder="5.25" value={r.cashTarget}
                onChange={(e) => setRungs(rungs.map((x, j) => j === i ? { ...x, cashTarget: e.target.value } : x))} /></div>
            <div><label style={S.lbl}>Bushels</label>
              <input style={{ ...S.inp, width: 110 }} inputMode="numeric" placeholder="5000" value={r.bushels}
                onChange={(e) => setRungs(rungs.map((x, j) => j === i ? { ...x, bushels: e.target.value } : x))} /></div>
            <div><label style={S.lbl}>Futures target</label>
              <div style={{ ...S.inp, width: 120, background: "#f6f8f6", fontWeight: 800,
                            color: fut == null ? "#8a5a00" : "#1a2e1a" }}>
                {fut == null ? (basis == null ? "pick a window" : "—") : gtMoney(fut)}</div></div>
            {rungs.length > 1 && (
              <button style={{ ...S.btn(false), padding: "6px 10px" }}
                onClick={() => setRungs(rungs.filter((_, j) => j !== i))}>Remove</button>)}
          </div>);
        })}
        {!editing && rungs.length < 4 && (
          <button style={{ ...S.btn(false), padding: "5px 11px", fontSize: 12 }}
            onClick={() => setRungs([...rungs, { cashTarget: "", bushels: "" }])}>+ Another rung</button>)}
      </div>

      <div><label style={{ ...S.lbl, marginTop: 14 }}>Trucking notes</label>
        <input style={{ ...S.inp, width: "100%" }} value={f.notes}
          onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>

      <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center", flexWrap: "wrap" }}>
        <button style={S.btn(true)} disabled={saving} onClick={save}>
          {saving ? "Saving…" : editing ? "Save changes" : "Save target"}</button>
        {editing && (
          <button style={S.btn(false)} onClick={onCancelEdit}>Cancel</button>)}
        <button style={S.btn(false)} disabled={!ready} onClick={() => onPrint(asTicket())}>🖨 Print sheet</button>
        {!ready && <span style={{ fontSize: 12, color: "#8a5a00" }}>
          Still needs {missing.join(", ")}.</span>}
        {chosen && (
          <span style={{ fontSize: 12, color: "#5d6b5d", marginLeft: "auto" }}>
            {f.deliveryPoint} · {chosen.ship_month} · {chosen.cme_contract} · basis {gtCents(basis)}
            {f.basisLocked ? " · locked by contract" : (sheetVersion ? ` · board ${sheetVersion}` : "")}</span>)}
      </div>
    </div>
  );
}

export default AppWithBoundary;
